/**
 * @license AngularJS v1.6.6
 * (c) 2010-2017 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window) {'use strict';

/* exported
  minErrConfig,
  errorHandlingConfig,
  isValidObjectMaxDepth
*/

var minErrConfig = {
  objectMaxDepth: 5
};

/**
 * @ngdoc function
 * @name angular.errorHandlingConfig
 * @module ng
 * @kind function
 *
 * @description
 * Configure several aspects of error handling in AngularJS if used as a setter or return the
 * current configuration if used as a getter. The following options are supported:
 *
 * - **objectMaxDepth**: The maximum depth to which objects are traversed when stringified for error messages.
 *
 * Omitted or undefined options will leave the corresponding configuration values unchanged.
 *
 * @param {Object=} config - The configuration object. May only contain the options that need to be
 *     updated. Supported keys:
 *
 * * `objectMaxDepth`  **{Number}** - The max depth for stringifying objects. Setting to a
 *   non-positive or non-numeric value, removes the max depth limit.
 *   Default: 5
 */
function errorHandlingConfig(config) {
  if (isObject(config)) {
    if (isDefined(config.objectMaxDepth)) {
      minErrConfig.objectMaxDepth = isValidObjectMaxDepth(config.objectMaxDepth) ? config.objectMaxDepth : NaN;
    }
  } else {
    return minErrConfig;
  }
}

/**
 * @private
 * @param {Number} maxDepth
 * @return {boolean}
 */
function isValidObjectMaxDepth(maxDepth) {
  return isNumber(maxDepth) && maxDepth > 0;
}

/**
 * @description
 *
 * This object provides a utility for producing rich Error messages within
 * Angular. It can be called as follows:
 *
 * var exampleMinErr = minErr('example');
 * throw exampleMinErr('one', 'This {0} is {1}', foo, bar);
 *
 * The above creates an instance of minErr in the example namespace. The
 * resulting error will have a namespaced error code of example.one.  The
 * resulting error will replace {0} with the value of foo, and {1} with the
 * value of bar. The object is not restricted in the number of arguments it can
 * take.
 *
 * If fewer arguments are specified than necessary for interpolation, the extra
 * interpolation markers will be preserved in the final string.
 *
 * Since data will be parsed statically during a build step, some restrictions
 * are applied with respect to how minErr instances are created and called.
 * Instances should have names of the form namespaceMinErr for a minErr created
 * using minErr('namespace') . Error codes, namespaces and template strings
 * should all be static strings, not variables or general expressions.
 *
 * @param {string} module The namespace to use for the new minErr instance.
 * @param {function} ErrorConstructor Custom error constructor to be instantiated when returning
 *   error from returned function, for cases when a particular type of error is useful.
 * @returns {function(code:string, template:string, ...templateArgs): Error} minErr instance
 */

function minErr(module, ErrorConstructor) {
  ErrorConstructor = ErrorConstructor || Error;
  return function() {
    var code = arguments[0],
      template = arguments[1],
      message = '[' + (module ? module + ':' : '') + code + '] ',
      templateArgs = sliceArgs(arguments, 2).map(function(arg) {
        return toDebugString(arg, minErrConfig.objectMaxDepth);
      }),
      paramPrefix, i;

    message += template.replace(/\{\d+\}/g, function(match) {
      var index = +match.slice(1, -1);

      if (index < templateArgs.length) {
        return templateArgs[index];
      }

      return match;
    });

    message += '\nhttp://errors.angularjs.org/1.6.6/' +
      (module ? module + '/' : '') + code;

    for (i = 0, paramPrefix = '?'; i < templateArgs.length; i++, paramPrefix = '&') {
      message += paramPrefix + 'p' + i + '=' + encodeURIComponent(templateArgs[i]);
    }

    return new ErrorConstructor(message);
  };
}

/* We need to tell ESLint what variables are being exported */
/* exported
  angular,
  msie,
  jqLite,
  jQuery,
  slice,
  splice,
  push,
  toString,
  minErrConfig,
  errorHandlingConfig,
  isValidObjectMaxDepth,
  ngMinErr,
  angularModule,
  uid,
  REGEX_STRING_REGEXP,
  VALIDITY_STATE_PROPERTY,

  lowercase,
  uppercase,
  manualLowercase,
  manualUppercase,
  nodeName_,
  isArrayLike,
  forEach,
  forEachSorted,
  reverseParams,
  nextUid,
  setHashKey,
  extend,
  toInt,
  inherit,
  merge,
  noop,
  identity,
  valueFn,
  isUndefined,
  isDefined,
  isObject,
  isBlankObject,
  isString,
  isNumber,
  isNumberNaN,
  isDate,
  isError,
  isArray,
  isFunction,
  isRegExp,
  isWindow,
  isScope,
  isFile,
  isFormData,
  isBlob,
  isBoolean,
  isPromiseLike,
  trim,
  escapeForRegexp,
  isElement,
  makeMap,
  includes,
  arrayRemove,
  copy,
  simpleCompare,
  equals,
  csp,
  jq,
  concat,
  sliceArgs,
  bind,
  toJsonReplacer,
  toJson,
  fromJson,
  convertTimezoneToLocal,
  timezoneToOffset,
  startingTag,
  tryDecodeURIComponent,
  parseKeyValue,
  toKeyValue,
  encodeUriSegment,
  encodeUriQuery,
  angularInit,
  bootstrap,
  getTestability,
  snake_case,
  bindJQuery,
  assertArg,
  assertArgFn,
  assertNotHasOwnProperty,
  getter,
  getBlockNodes,
  hasOwnProperty,
  createMap,
  stringify,

  NODE_TYPE_ELEMENT,
  NODE_TYPE_ATTRIBUTE,
  NODE_TYPE_TEXT,
  NODE_TYPE_COMMENT,
  NODE_TYPE_DOCUMENT,
  NODE_TYPE_DOCUMENT_FRAGMENT
*/

////////////////////////////////////

/**
 * @ngdoc module
 * @name ng
 * @module ng
 * @installation
 * @description
 *
 * # ng (core module)
 * The ng module is loaded by default when an AngularJS application is started. The module itself
 * contains the essential components for an AngularJS application to function. The table below
 * lists a high level breakdown of each of the services/factories, filters, directives and testing
 * components available within this core module.
 *
 * <div doc-module-components="ng"></div>
 */

var REGEX_STRING_REGEXP = /^\/(.+)\/([a-z]*)$/;

// The name of a form control's ValidityState property.
// This is used so that it's possible for internal tests to create mock ValidityStates.
var VALIDITY_STATE_PROPERTY = 'validity';


var hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * @ngdoc function
 * @name angular.lowercase
 * @module ng
 * @kind function
 *
 * @deprecated
 * sinceVersion="1.5.0"
 * removeVersion="1.7.0"
 * Use [String.prototype.toLowerCase](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/String/toLowerCase) instead.
 *
 * @description Converts the specified string to lowercase.
 * @param {string} string String to be converted to lowercase.
 * @returns {string} Lowercased string.
 */
var lowercase = function(string) {return isString(string) ? string.toLowerCase() : string;};

/**
 * @ngdoc function
 * @name angular.uppercase
 * @module ng
 * @kind function
 *
 * @deprecated
 * sinceVersion="1.5.0"
 * removeVersion="1.7.0"
 * Use [String.prototype.toUpperCase](https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/String/toUpperCase) instead.
 *
 * @description Converts the specified string to uppercase.
 * @param {string} string String to be converted to uppercase.
 * @returns {string} Uppercased string.
 */
var uppercase = function(string) {return isString(string) ? string.toUpperCase() : string;};


var manualLowercase = function(s) {
  /* eslint-disable no-bitwise */
  return isString(s)
      ? s.replace(/[A-Z]/g, function(ch) {return String.fromCharCode(ch.charCodeAt(0) | 32);})
      : s;
  /* eslint-enable */
};
var manualUppercase = function(s) {
  /* eslint-disable no-bitwise */
  return isString(s)
      ? s.replace(/[a-z]/g, function(ch) {return String.fromCharCode(ch.charCodeAt(0) & ~32);})
      : s;
  /* eslint-enable */
};


// String#toLowerCase and String#toUpperCase don't produce correct results in browsers with Turkish
// locale, for this reason we need to detect this case and redefine lowercase/uppercase methods
// with correct but slower alternatives. See https://github.com/angular/angular.js/issues/11387
if ('i' !== 'I'.toLowerCase()) {
  lowercase = manualLowercase;
  uppercase = manualUppercase;
}


var
    msie,             // holds major version number for IE, or NaN if UA is not IE.
    jqLite,           // delay binding since jQuery could be loaded after us.
    jQuery,           // delay binding
    slice             = [].slice,
    splice            = [].splice,
    push              = [].push,
    toString          = Object.prototype.toString,
    getPrototypeOf    = Object.getPrototypeOf,
    ngMinErr          = minErr('ng'),

    /** @name angular */
    angular           = window.angular || (window.angular = {}),
    angularModule,
    uid               = 0;

// Support: IE 9-11 only
/**
 * documentMode is an IE-only property
 * http://msdn.microsoft.com/en-us/library/ie/cc196988(v=vs.85).aspx
 */
msie = window.document.documentMode;


/**
 * @private
 * @param {*} obj
 * @return {boolean} Returns true if `obj` is an array or array-like object (NodeList, Arguments,
 *                   String ...)
 */
function isArrayLike(obj) {

  // `null`, `undefined` and `window` are not array-like
  if (obj == null || isWindow(obj)) return false;

  // arrays, strings and jQuery/jqLite objects are array like
  // * jqLite is either the jQuery or jqLite constructor function
  // * we have to check the existence of jqLite first as this method is called
  //   via the forEach method when constructing the jqLite object in the first place
  if (isArray(obj) || isString(obj) || (jqLite && obj instanceof jqLite)) return true;

  // Support: iOS 8.2 (not reproducible in simulator)
  // "length" in obj used to prevent JIT error (gh-11508)
  var length = 'length' in Object(obj) && obj.length;

  // NodeList objects (with `item` method) and
  // other objects with suitable length characteristics are array-like
  return isNumber(length) &&
    (length >= 0 && ((length - 1) in obj || obj instanceof Array) || typeof obj.item === 'function');

}

/**
 * @ngdoc function
 * @name angular.forEach
 * @module ng
 * @kind function
 *
 * @description
 * Invokes the `iterator` function once for each item in `obj` collection, which can be either an
 * object or an array. The `iterator` function is invoked with `iterator(value, key, obj)`, where `value`
 * is the value of an object property or an array element, `key` is the object property key or
 * array element index and obj is the `obj` itself. Specifying a `context` for the function is optional.
 *
 * It is worth noting that `.forEach` does not iterate over inherited properties because it filters
 * using the `hasOwnProperty` method.
 *
 * Unlike ES262's
 * [Array.prototype.forEach](http://www.ecma-international.org/ecma-262/5.1/#sec-15.4.4.18),
 * providing 'undefined' or 'null' values for `obj` will not throw a TypeError, but rather just
 * return the value provided.
 *
   ```js
     var values = {name: 'misko', gender: 'male'};
     var log = [];
     angular.forEach(values, function(value, key) {
       this.push(key + ': ' + value);
     }, log);
     expect(log).toEqual(['name: misko', 'gender: male']);
   ```
 *
 * @param {Object|Array} obj Object to iterate over.
 * @param {Function} iterator Iterator function.
 * @param {Object=} context Object to become context (`this`) for the iterator function.
 * @returns {Object|Array} Reference to `obj`.
 */

function forEach(obj, iterator, context) {
  var key, length;
  if (obj) {
    if (isFunction(obj)) {
      for (key in obj) {
        if (key !== 'prototype' && key !== 'length' && key !== 'name' && obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (isArray(obj) || isArrayLike(obj)) {
      var isPrimitive = typeof obj !== 'object';
      for (key = 0, length = obj.length; key < length; key++) {
        if (isPrimitive || key in obj) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else if (obj.forEach && obj.forEach !== forEach) {
        obj.forEach(iterator, context, obj);
    } else if (isBlankObject(obj)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in obj) {
        iterator.call(context, obj[key], key, obj);
      }
    } else if (typeof obj.hasOwnProperty === 'function') {
      // Slow path for objects inheriting Object.prototype, hasOwnProperty check needed
      for (key in obj) {
        if (obj.hasOwnProperty(key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    } else {
      // Slow path for objects which do not have a method `hasOwnProperty`
      for (key in obj) {
        if (hasOwnProperty.call(obj, key)) {
          iterator.call(context, obj[key], key, obj);
        }
      }
    }
  }
  return obj;
}

function forEachSorted(obj, iterator, context) {
  var keys = Object.keys(obj).sort();
  for (var i = 0; i < keys.length; i++) {
    iterator.call(context, obj[keys[i]], keys[i]);
  }
  return keys;
}


/**
 * when using forEach the params are value, key, but it is often useful to have key, value.
 * @param {function(string, *)} iteratorFn
 * @returns {function(*, string)}
 */
function reverseParams(iteratorFn) {
  return function(value, key) {iteratorFn(key, value);};
}

/**
 * A consistent way of creating unique IDs in angular.
 *
 * Using simple numbers allows us to generate 28.6 million unique ids per second for 10 years before
 * we hit number precision issues in JavaScript.
 *
 * Math.pow(2,53) / 60 / 60 / 24 / 365 / 10 = 28.6M
 *
 * @returns {number} an unique alpha-numeric string
 */
function nextUid() {
  return ++uid;
}


/**
 * Set or clear the hashkey for an object.
 * @param obj object
 * @param h the hashkey (!truthy to delete the hashkey)
 */
function setHashKey(obj, h) {
  if (h) {
    obj.$$hashKey = h;
  } else {
    delete obj.$$hashKey;
  }
}


function baseExtend(dst, objs, deep) {
  var h = dst.$$hashKey;

  for (var i = 0, ii = objs.length; i < ii; ++i) {
    var obj = objs[i];
    if (!isObject(obj) && !isFunction(obj)) continue;
    var keys = Object.keys(obj);
    for (var j = 0, jj = keys.length; j < jj; j++) {
      var key = keys[j];
      var src = obj[key];

      if (deep && isObject(src)) {
        if (isDate(src)) {
          dst[key] = new Date(src.valueOf());
        } else if (isRegExp(src)) {
          dst[key] = new RegExp(src);
        } else if (src.nodeName) {
          dst[key] = src.cloneNode(true);
        } else if (isElement(src)) {
          dst[key] = src.clone();
        } else {
          if (!isObject(dst[key])) dst[key] = isArray(src) ? [] : {};
          baseExtend(dst[key], [src], true);
        }
      } else {
        dst[key] = src;
      }
    }
  }

  setHashKey(dst, h);
  return dst;
}

/**
 * @ngdoc function
 * @name angular.extend
 * @module ng
 * @kind function
 *
 * @description
 * Extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
 * to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
 * by passing an empty object as the target: `var object = angular.extend({}, object1, object2)`.
 *
 * **Note:** Keep in mind that `angular.extend` does not support recursive merge (deep copy). Use
 * {@link angular.merge} for this.
 *
 * @param {Object} dst Destination object.
 * @param {...Object} src Source object(s).
 * @returns {Object} Reference to `dst`.
 */
function extend(dst) {
  return baseExtend(dst, slice.call(arguments, 1), false);
}


/**
* @ngdoc function
* @name angular.merge
* @module ng
* @kind function
*
* @description
* Deeply extends the destination object `dst` by copying own enumerable properties from the `src` object(s)
* to `dst`. You can specify multiple `src` objects. If you want to preserve original objects, you can do so
* by passing an empty object as the target: `var object = angular.merge({}, object1, object2)`.
*
* Unlike {@link angular.extend extend()}, `merge()` recursively descends into object properties of source
* objects, performing a deep copy.
*
* @deprecated
* sinceVersion="1.6.5"
* This function is deprecated, but will not be removed in the 1.x lifecycle.
* There are edge cases (see {@link angular.merge#known-issues known issues}) that are not
* supported by this function. We suggest
* using [lodash's merge()](https://lodash.com/docs/4.17.4#merge) instead.
*
* @knownIssue
* This is a list of (known) object types that are not handled correctly by this function:
* - [`Blob`](https://developer.mozilla.org/docs/Web/API/Blob)
* - [`MediaStream`](https://developer.mozilla.org/docs/Web/API/MediaStream)
* - [`CanvasGradient`](https://developer.mozilla.org/docs/Web/API/CanvasGradient)
* - AngularJS {@link $rootScope.Scope scopes};
*
* @param {Object} dst Destination object.
* @param {...Object} src Source object(s).
* @returns {Object} Reference to `dst`.
*/
function merge(dst) {
  return baseExtend(dst, slice.call(arguments, 1), true);
}



function toInt(str) {
  return parseInt(str, 10);
}

var isNumberNaN = Number.isNaN || function isNumberNaN(num) {
  // eslint-disable-next-line no-self-compare
  return num !== num;
};


function inherit(parent, extra) {
  return extend(Object.create(parent), extra);
}

/**
 * @ngdoc function
 * @name angular.noop
 * @module ng
 * @kind function
 *
 * @description
 * A function that performs no operations. This function can be useful when writing code in the
 * functional style.
   ```js
     function foo(callback) {
       var result = calculateResult();
       (callback || angular.noop)(result);
     }
   ```
 */
function noop() {}
noop.$inject = [];


/**
 * @ngdoc function
 * @name angular.identity
 * @module ng
 * @kind function
 *
 * @description
 * A function that returns its first argument. This function is useful when writing code in the
 * functional style.
 *
   ```js
   function transformer(transformationFn, value) {
     return (transformationFn || angular.identity)(value);
   };

   // E.g.
   function getResult(fn, input) {
     return (fn || angular.identity)(input);
   };

   getResult(function(n) { return n * 2; }, 21);   // returns 42
   getResult(null, 21);                            // returns 21
   getResult(undefined, 21);                       // returns 21
   ```
 *
 * @param {*} value to be returned.
 * @returns {*} the value passed in.
 */
function identity($) {return $;}
identity.$inject = [];


function valueFn(value) {return function valueRef() {return value;};}

function hasCustomToString(obj) {
  return isFunction(obj.toString) && obj.toString !== toString;
}


/**
 * @ngdoc function
 * @name angular.isUndefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is undefined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is undefined.
 */
function isUndefined(value) {return typeof value === 'undefined';}


/**
 * @ngdoc function
 * @name angular.isDefined
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is defined.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is defined.
 */
function isDefined(value) {return typeof value !== 'undefined';}


/**
 * @ngdoc function
 * @name angular.isObject
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Object`. Unlike `typeof` in JavaScript, `null`s are not
 * considered to be objects. Note that JavaScript arrays are objects.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Object` but not `null`.
 */
function isObject(value) {
  // http://jsperf.com/isobject4
  return value !== null && typeof value === 'object';
}


/**
 * Determine if a value is an object with a null prototype
 *
 * @returns {boolean} True if `value` is an `Object` with a null prototype
 */
function isBlankObject(value) {
  return value !== null && typeof value === 'object' && !getPrototypeOf(value);
}


/**
 * @ngdoc function
 * @name angular.isString
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `String`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `String`.
 */
function isString(value) {return typeof value === 'string';}


/**
 * @ngdoc function
 * @name angular.isNumber
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Number`.
 *
 * This includes the "special" numbers `NaN`, `+Infinity` and `-Infinity`.
 *
 * If you wish to exclude these then you can use the native
 * [`isFinite'](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/isFinite)
 * method.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Number`.
 */
function isNumber(value) {return typeof value === 'number';}


/**
 * @ngdoc function
 * @name angular.isDate
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a value is a date.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Date`.
 */
function isDate(value) {
  return toString.call(value) === '[object Date]';
}


/**
 * @ngdoc function
 * @name angular.isArray
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is an `Array`. Alias of Array.isArray.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Array`.
 */
var isArray = Array.isArray;

/**
 * @description
 * Determines if a reference is an `Error`.
 * Loosely based on https://www.npmjs.com/package/iserror
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is an `Error`.
 */
function isError(value) {
  var tag = toString.call(value);
  switch (tag) {
    case '[object Error]': return true;
    case '[object Exception]': return true;
    case '[object DOMException]': return true;
    default: return value instanceof Error;
  }
}

/**
 * @ngdoc function
 * @name angular.isFunction
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a `Function`.
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `Function`.
 */
function isFunction(value) {return typeof value === 'function';}


/**
 * Determines if a value is a regular expression object.
 *
 * @private
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a `RegExp`.
 */
function isRegExp(value) {
  return toString.call(value) === '[object RegExp]';
}


/**
 * Checks if `obj` is a window object.
 *
 * @private
 * @param {*} obj Object to check
 * @returns {boolean} True if `obj` is a window obj.
 */
function isWindow(obj) {
  return obj && obj.window === obj;
}


function isScope(obj) {
  return obj && obj.$evalAsync && obj.$watch;
}


function isFile(obj) {
  return toString.call(obj) === '[object File]';
}


function isFormData(obj) {
  return toString.call(obj) === '[object FormData]';
}


function isBlob(obj) {
  return toString.call(obj) === '[object Blob]';
}


function isBoolean(value) {
  return typeof value === 'boolean';
}


function isPromiseLike(obj) {
  return obj && isFunction(obj.then);
}


var TYPED_ARRAY_REGEXP = /^\[object (?:Uint8|Uint8Clamped|Uint16|Uint32|Int8|Int16|Int32|Float32|Float64)Array]$/;
function isTypedArray(value) {
  return value && isNumber(value.length) && TYPED_ARRAY_REGEXP.test(toString.call(value));
}

function isArrayBuffer(obj) {
  return toString.call(obj) === '[object ArrayBuffer]';
}


var trim = function(value) {
  return isString(value) ? value.trim() : value;
};

// Copied from:
// http://docs.closure-library.googlecode.com/git/local_closure_goog_string_string.js.source.html#line1021
// Prereq: s is a string.
var escapeForRegexp = function(s) {
  return s
    .replace(/([-()[\]{}+?*.$^|,:#<!\\])/g, '\\$1')
    // eslint-disable-next-line no-control-regex
    .replace(/\x08/g, '\\x08');
};


/**
 * @ngdoc function
 * @name angular.isElement
 * @module ng
 * @kind function
 *
 * @description
 * Determines if a reference is a DOM element (or wrapped jQuery element).
 *
 * @param {*} value Reference to check.
 * @returns {boolean} True if `value` is a DOM element (or wrapped jQuery element).
 */
function isElement(node) {
  return !!(node &&
    (node.nodeName  // We are a direct element.
    || (node.prop && node.attr && node.find)));  // We have an on and find method part of jQuery API.
}

/**
 * @param str 'key1,key2,...'
 * @returns {object} in the form of {key1:true, key2:true, ...}
 */
function makeMap(str) {
  var obj = {}, items = str.split(','), i;
  for (i = 0; i < items.length; i++) {
    obj[items[i]] = true;
  }
  return obj;
}


function nodeName_(element) {
  return lowercase(element.nodeName || (element[0] && element[0].nodeName));
}

function includes(array, obj) {
  return Array.prototype.indexOf.call(array, obj) !== -1;
}

function arrayRemove(array, value) {
  var index = array.indexOf(value);
  if (index >= 0) {
    array.splice(index, 1);
  }
  return index;
}

/**
 * @ngdoc function
 * @name angular.copy
 * @module ng
 * @kind function
 *
 * @description
 * Creates a deep copy of `source`, which should be an object or an array.
 *
 * * If no destination is supplied, a copy of the object or array is created.
 * * If a destination is provided, all of its elements (for arrays) or properties (for objects)
 *   are deleted and then all elements/properties from the source are copied to it.
 * * If `source` is not an object or array (inc. `null` and `undefined`), `source` is returned.
 * * If `source` is identical to `destination` an exception will be thrown.
 *
 * <br />
 * <div class="alert alert-warning">
 *   Only enumerable properties are taken into account. Non-enumerable properties (both on `source`
 *   and on `destination`) will be ignored.
 * </div>
 *
 * @param {*} source The source that will be used to make a copy.
 *                   Can be any type, including primitives, `null`, and `undefined`.
 * @param {(Object|Array)=} destination Destination into which the source is copied. If
 *     provided, must be of the same type as `source`.
 * @returns {*} The copy or updated `destination`, if `destination` was specified.
 *
 * @example
  <example module="copyExample" name="angular-copy">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate class="simple-form">
          <label>Name: <input type="text" ng-model="user.name" /></label><br />
          <label>Age:  <input type="number" ng-model="user.age" /></label><br />
          Gender: <label><input type="radio" ng-model="user.gender" value="male" />male</label>
                  <label><input type="radio" ng-model="user.gender" value="female" />female</label><br />
          <button ng-click="reset()">RESET</button>
          <button ng-click="update(user)">SAVE</button>
        </form>
        <pre>form = {{user | json}}</pre>
        <pre>master = {{master | json}}</pre>
      </div>
    </file>
    <file name="script.js">
      // Module: copyExample
      angular.
        module('copyExample', []).
        controller('ExampleController', ['$scope', function($scope) {
          $scope.master = {};

          $scope.reset = function() {
            // Example with 1 argument
            $scope.user = angular.copy($scope.master);
          };

          $scope.update = function(user) {
            // Example with 2 arguments
            angular.copy(user, $scope.master);
          };

          $scope.reset();
        }]);
    </file>
  </example>
 */
function copy(source, destination, maxDepth) {
  var stackSource = [];
  var stackDest = [];
  maxDepth = isValidObjectMaxDepth(maxDepth) ? maxDepth : NaN;

  if (destination) {
    if (isTypedArray(destination) || isArrayBuffer(destination)) {
      throw ngMinErr('cpta', 'Can\'t copy! TypedArray destination cannot be mutated.');
    }
    if (source === destination) {
      throw ngMinErr('cpi', 'Can\'t copy! Source and destination are identical.');
    }

    // Empty the destination object
    if (isArray(destination)) {
      destination.length = 0;
    } else {
      forEach(destination, function(value, key) {
        if (key !== '$$hashKey') {
          delete destination[key];
        }
      });
    }

    stackSource.push(source);
    stackDest.push(destination);
    return copyRecurse(source, destination, maxDepth);
  }

  return copyElement(source, maxDepth);

  function copyRecurse(source, destination, maxDepth) {
    maxDepth--;
    if (maxDepth < 0) {
      return '...';
    }
    var h = destination.$$hashKey;
    var key;
    if (isArray(source)) {
      for (var i = 0, ii = source.length; i < ii; i++) {
        destination.push(copyElement(source[i], maxDepth));
      }
    } else if (isBlankObject(source)) {
      // createMap() fast path --- Safe to avoid hasOwnProperty check because prototype chain is empty
      for (key in source) {
        destination[key] = copyElement(source[key], maxDepth);
      }
    } else if (source && typeof source.hasOwnProperty === 'function') {
      // Slow path, which must rely on hasOwnProperty
      for (key in source) {
        if (source.hasOwnProperty(key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    } else {
      // Slowest path --- hasOwnProperty can't be called as a method
      for (key in source) {
        if (hasOwnProperty.call(source, key)) {
          destination[key] = copyElement(source[key], maxDepth);
        }
      }
    }
    setHashKey(destination, h);
    return destination;
  }

  function copyElement(source, maxDepth) {
    // Simple values
    if (!isObject(source)) {
      return source;
    }

    // Already copied values
    var index = stackSource.indexOf(source);
    if (index !== -1) {
      return stackDest[index];
    }

    if (isWindow(source) || isScope(source)) {
      throw ngMinErr('cpws',
        'Can\'t copy! Making copies of Window or Scope instances is not supported.');
    }

    var needsRecurse = false;
    var destination = copyType(source);

    if (destination === undefined) {
      destination = isArray(source) ? [] : Object.create(getPrototypeOf(source));
      needsRecurse = true;
    }

    stackSource.push(source);
    stackDest.push(destination);

    return needsRecurse
      ? copyRecurse(source, destination, maxDepth)
      : destination;
  }

  function copyType(source) {
    switch (toString.call(source)) {
      case '[object Int8Array]':
      case '[object Int16Array]':
      case '[object Int32Array]':
      case '[object Float32Array]':
      case '[object Float64Array]':
      case '[object Uint8Array]':
      case '[object Uint8ClampedArray]':
      case '[object Uint16Array]':
      case '[object Uint32Array]':
        return new source.constructor(copyElement(source.buffer), source.byteOffset, source.length);

      case '[object ArrayBuffer]':
        // Support: IE10
        if (!source.slice) {
          // If we're in this case we know the environment supports ArrayBuffer
          /* eslint-disable no-undef */
          var copied = new ArrayBuffer(source.byteLength);
          new Uint8Array(copied).set(new Uint8Array(source));
          /* eslint-enable */
          return copied;
        }
        return source.slice(0);

      case '[object Boolean]':
      case '[object Number]':
      case '[object String]':
      case '[object Date]':
        return new source.constructor(source.valueOf());

      case '[object RegExp]':
        var re = new RegExp(source.source, source.toString().match(/[^/]*$/)[0]);
        re.lastIndex = source.lastIndex;
        return re;

      case '[object Blob]':
        return new source.constructor([source], {type: source.type});
    }

    if (isFunction(source.cloneNode)) {
      return source.cloneNode(true);
    }
  }
}


// eslint-disable-next-line no-self-compare
function simpleCompare(a, b) { return a === b || (a !== a && b !== b); }


/**
 * @ngdoc function
 * @name angular.equals
 * @module ng
 * @kind function
 *
 * @description
 * Determines if two objects or two values are equivalent. Supports value types, regular
 * expressions, arrays and objects.
 *
 * Two objects or values are considered equivalent if at least one of the following is true:
 *
 * * Both objects or values pass `===` comparison.
 * * Both objects or values are of the same type and all of their properties are equal by
 *   comparing them with `angular.equals`.
 * * Both values are NaN. (In JavaScript, NaN == NaN => false. But we consider two NaN as equal)
 * * Both values represent the same regular expression (In JavaScript,
 *   /abc/ == /abc/ => false. But we consider two regular expressions as equal when their textual
 *   representation matches).
 *
 * During a property comparison, properties of `function` type and properties with names
 * that begin with `$` are ignored.
 *
 * Scope and DOMWindow objects are being compared only by identify (`===`).
 *
 * @param {*} o1 Object or value to compare.
 * @param {*} o2 Object or value to compare.
 * @returns {boolean} True if arguments are equal.
 *
 * @example
   <example module="equalsExample" name="equalsExample">
     <file name="index.html">
      <div ng-controller="ExampleController">
        <form novalidate>
          <h3>User 1</h3>
          Name: <input type="text" ng-model="user1.name">
          Age: <input type="number" ng-model="user1.age">

          <h3>User 2</h3>
          Name: <input type="text" ng-model="user2.name">
          Age: <input type="number" ng-model="user2.age">

          <div>
            <br/>
            <input type="button" value="Compare" ng-click="compare()">
          </div>
          User 1: <pre>{{user1 | json}}</pre>
          User 2: <pre>{{user2 | json}}</pre>
          Equal: <pre>{{result}}</pre>
        </form>
      </div>
    </file>
    <file name="script.js">
        angular.module('equalsExample', []).controller('ExampleController', ['$scope', function($scope) {
          $scope.user1 = {};
          $scope.user2 = {};
          $scope.compare = function() {
            $scope.result = angular.equals($scope.user1, $scope.user2);
          };
        }]);
    </file>
  </example>
 */
function equals(o1, o2) {
  if (o1 === o2) return true;
  if (o1 === null || o2 === null) return false;
  // eslint-disable-next-line no-self-compare
  if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
  var t1 = typeof o1, t2 = typeof o2, length, key, keySet;
  if (t1 === t2 && t1 === 'object') {
    if (isArray(o1)) {
      if (!isArray(o2)) return false;
      if ((length = o1.length) === o2.length) {
        for (key = 0; key < length; key++) {
          if (!equals(o1[key], o2[key])) return false;
        }
        return true;
      }
    } else if (isDate(o1)) {
      if (!isDate(o2)) return false;
      return simpleCompare(o1.getTime(), o2.getTime());
    } else if (isRegExp(o1)) {
      if (!isRegExp(o2)) return false;
      return o1.toString() === o2.toString();
    } else {
      if (isScope(o1) || isScope(o2) || isWindow(o1) || isWindow(o2) ||
        isArray(o2) || isDate(o2) || isRegExp(o2)) return false;
      keySet = createMap();
      for (key in o1) {
        if (key.charAt(0) === '$' || isFunction(o1[key])) continue;
        if (!equals(o1[key], o2[key])) return false;
        keySet[key] = true;
      }
      for (key in o2) {
        if (!(key in keySet) &&
            key.charAt(0) !== '$' &&
            isDefined(o2[key]) &&
            !isFunction(o2[key])) return false;
      }
      return true;
    }
  }
  return false;
}

var csp = function() {
  if (!isDefined(csp.rules)) {


    var ngCspElement = (window.document.querySelector('[ng-csp]') ||
                    window.document.querySelector('[data-ng-csp]'));

    if (ngCspElement) {
      var ngCspAttribute = ngCspElement.getAttribute('ng-csp') ||
                    ngCspElement.getAttribute('data-ng-csp');
      csp.rules = {
        noUnsafeEval: !ngCspAttribute || (ngCspAttribute.indexOf('no-unsafe-eval') !== -1),
        noInlineStyle: !ngCspAttribute || (ngCspAttribute.indexOf('no-inline-style') !== -1)
      };
    } else {
      csp.rules = {
        noUnsafeEval: noUnsafeEval(),
        noInlineStyle: false
      };
    }
  }

  return csp.rules;

  function noUnsafeEval() {
    try {
      // eslint-disable-next-line no-new, no-new-func
      new Function('');
      return false;
    } catch (e) {
      return true;
    }
  }
};

/**
 * @ngdoc directive
 * @module ng
 * @name ngJq
 *
 * @element ANY
 * @param {string=} ngJq the name of the library available under `window`
 * to be used for angular.element
 * @description
 * Use this directive to force the angular.element library.  This should be
 * used to force either jqLite by leaving ng-jq blank or setting the name of
 * the jquery variable under window (eg. jQuery).
 *
 * Since angular looks for this directive when it is loaded (doesn't wait for the
 * DOMContentLoaded event), it must be placed on an element that comes before the script
 * which loads angular. Also, only the first instance of `ng-jq` will be used and all
 * others ignored.
 *
 * @example
 * This example shows how to force jqLite using the `ngJq` directive to the `html` tag.
 ```html
 <!doctype html>
 <html ng-app ng-jq>
 ...
 ...
 </html>
 ```
 * @example
 * This example shows how to use a jQuery based library of a different name.
 * The library name must be available at the top most 'window'.
 ```html
 <!doctype html>
 <html ng-app ng-jq="jQueryLib">
 ...
 ...
 </html>
 ```
 */
var jq = function() {
  if (isDefined(jq.name_)) return jq.name_;
  var el;
  var i, ii = ngAttrPrefixes.length, prefix, name;
  for (i = 0; i < ii; ++i) {
    prefix = ngAttrPrefixes[i];
    el = window.document.querySelector('[' + prefix.replace(':', '\\:') + 'jq]');
    if (el) {
      name = el.getAttribute(prefix + 'jq');
      break;
    }
  }

  return (jq.name_ = name);
};

function concat(array1, array2, index) {
  return array1.concat(slice.call(array2, index));
}

function sliceArgs(args, startIndex) {
  return slice.call(args, startIndex || 0);
}


/**
 * @ngdoc function
 * @name angular.bind
 * @module ng
 * @kind function
 *
 * @description
 * Returns a function which calls function `fn` bound to `self` (`self` becomes the `this` for
 * `fn`). You can supply optional `args` that are prebound to the function. This feature is also
 * known as [partial application](http://en.wikipedia.org/wiki/Partial_application), as
 * distinguished from [function currying](http://en.wikipedia.org/wiki/Currying#Contrast_with_partial_function_application).
 *
 * @param {Object} self Context which `fn` should be evaluated in.
 * @param {function()} fn Function to be bound.
 * @param {...*} args Optional arguments to be prebound to the `fn` function call.
 * @returns {function()} Function that wraps the `fn` with all the specified bindings.
 */
function bind(self, fn) {
  var curryArgs = arguments.length > 2 ? sliceArgs(arguments, 2) : [];
  if (isFunction(fn) && !(fn instanceof RegExp)) {
    return curryArgs.length
      ? function() {
          return arguments.length
            ? fn.apply(self, concat(curryArgs, arguments, 0))
            : fn.apply(self, curryArgs);
        }
      : function() {
          return arguments.length
            ? fn.apply(self, arguments)
            : fn.call(self);
        };
  } else {
    // In IE, native methods are not functions so they cannot be bound (note: they don't need to be).
    return fn;
  }
}


function toJsonReplacer(key, value) {
  var val = value;

  if (typeof key === 'string' && key.charAt(0) === '$' && key.charAt(1) === '$') {
    val = undefined;
  } else if (isWindow(value)) {
    val = '$WINDOW';
  } else if (value &&  window.document === value) {
    val = '$DOCUMENT';
  } else if (isScope(value)) {
    val = '$SCOPE';
  }

  return val;
}


/**
 * @ngdoc function
 * @name angular.toJson
 * @module ng
 * @kind function
 *
 * @description
 * Serializes input into a JSON-formatted string. Properties with leading $$ characters will be
 * stripped since angular uses this notation internally.
 *
 * @param {Object|Array|Date|string|number|boolean} obj Input to be serialized into JSON.
 * @param {boolean|number} [pretty=2] If set to true, the JSON output will contain newlines and whitespace.
 *    If set to an integer, the JSON output will contain that many spaces per indentation.
 * @returns {string|undefined} JSON-ified string representing `obj`.
 * @knownIssue
 *
 * The Safari browser throws a `RangeError` instead of returning `null` when it tries to stringify a `Date`
 * object with an invalid date value. The only reliable way to prevent this is to monkeypatch the
 * `Date.prototype.toJSON` method as follows:
 *
 * ```
 * var _DatetoJSON = Date.prototype.toJSON;
 * Date.prototype.toJSON = function() {
 *   try {
 *     return _DatetoJSON.call(this);
 *   } catch(e) {
 *     if (e instanceof RangeError) {
 *       return null;
 *     }
 *     throw e;
 *   }
 * };
 * ```
 *
 * See https://github.com/angular/angular.js/pull/14221 for more information.
 */
function toJson(obj, pretty) {
  if (isUndefined(obj)) return undefined;
  if (!isNumber(pretty)) {
    pretty = pretty ? 2 : null;
  }
  return JSON.stringify(obj, toJsonReplacer, pretty);
}


/**
 * @ngdoc function
 * @name angular.fromJson
 * @module ng
 * @kind function
 *
 * @description
 * Deserializes a JSON string.
 *
 * @param {string} json JSON string to deserialize.
 * @returns {Object|Array|string|number} Deserialized JSON string.
 */
function fromJson(json) {
  return isString(json)
      ? JSON.parse(json)
      : json;
}


var ALL_COLONS = /:/g;
function timezoneToOffset(timezone, fallback) {
  // Support: IE 9-11 only, Edge 13-15+
  // IE/Edge do not "understand" colon (`:`) in timezone
  timezone = timezone.replace(ALL_COLONS, '');
  var requestedTimezoneOffset = Date.parse('Jan 01, 1970 00:00:00 ' + timezone) / 60000;
  return isNumberNaN(requestedTimezoneOffset) ? fallback : requestedTimezoneOffset;
}


function addDateMinutes(date, minutes) {
  date = new Date(date.getTime());
  date.setMinutes(date.getMinutes() + minutes);
  return date;
}


function convertTimezoneToLocal(date, timezone, reverse) {
  reverse = reverse ? -1 : 1;
  var dateTimezoneOffset = date.getTimezoneOffset();
  var timezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
  return addDateMinutes(date, reverse * (timezoneOffset - dateTimezoneOffset));
}


/**
 * @returns {string} Returns the string representation of the element.
 */
function startingTag(element) {
  element = jqLite(element).clone().empty();
  var elemHtml = jqLite('<div>').append(element).html();
  try {
    return element[0].nodeType === NODE_TYPE_TEXT ? lowercase(elemHtml) :
        elemHtml.
          match(/^(<[^>]+>)/)[1].
          replace(/^<([\w-]+)/, function(match, nodeName) {return '<' + lowercase(nodeName);});
  } catch (e) {
    return lowercase(elemHtml);
  }

}


/////////////////////////////////////////////////

/**
 * Tries to decode the URI component without throwing an exception.
 *
 * @private
 * @param str value potential URI component to check.
 * @returns {boolean} True if `value` can be decoded
 * with the decodeURIComponent function.
 */
function tryDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // Ignore any invalid uri component.
  }
}


/**
 * Parses an escaped url query string into key-value pairs.
 * @returns {Object.<string,boolean|Array>}
 */
function parseKeyValue(/**string*/keyValue) {
  var obj = {};
  forEach((keyValue || '').split('&'), function(keyValue) {
    var splitPoint, key, val;
    if (keyValue) {
      key = keyValue = keyValue.replace(/\+/g,'%20');
      splitPoint = keyValue.indexOf('=');
      if (splitPoint !== -1) {
        key = keyValue.substring(0, splitPoint);
        val = keyValue.substring(splitPoint + 1);
      }
      key = tryDecodeURIComponent(key);
      if (isDefined(key)) {
        val = isDefined(val) ? tryDecodeURIComponent(val) : true;
        if (!hasOwnProperty.call(obj, key)) {
          obj[key] = val;
        } else if (isArray(obj[key])) {
          obj[key].push(val);
        } else {
          obj[key] = [obj[key],val];
        }
      }
    }
  });
  return obj;
}

function toKeyValue(obj) {
  var parts = [];
  forEach(obj, function(value, key) {
    if (isArray(value)) {
      forEach(value, function(arrayValue) {
        parts.push(encodeUriQuery(key, true) +
                   (arrayValue === true ? '' : '=' + encodeUriQuery(arrayValue, true)));
      });
    } else {
    parts.push(encodeUriQuery(key, true) +
               (value === true ? '' : '=' + encodeUriQuery(value, true)));
    }
  });
  return parts.length ? parts.join('&') : '';
}


/**
 * We need our custom method because encodeURIComponent is too aggressive and doesn't follow
 * http://www.ietf.org/rfc/rfc3986.txt with regards to the character set (pchar) allowed in path
 * segments:
 *    segment       = *pchar
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriSegment(val) {
  return encodeUriQuery(val, true).
             replace(/%26/gi, '&').
             replace(/%3D/gi, '=').
             replace(/%2B/gi, '+');
}


/**
 * This method is intended for encoding *key* or *value* parts of query component. We need a custom
 * method because encodeURIComponent is too aggressive and encodes stuff that doesn't have to be
 * encoded per http://tools.ietf.org/html/rfc3986:
 *    query         = *( pchar / "/" / "?" )
 *    pchar         = unreserved / pct-encoded / sub-delims / ":" / "@"
 *    unreserved    = ALPHA / DIGIT / "-" / "." / "_" / "~"
 *    pct-encoded   = "%" HEXDIG HEXDIG
 *    sub-delims    = "!" / "$" / "&" / "'" / "(" / ")"
 *                     / "*" / "+" / "," / ";" / "="
 */
function encodeUriQuery(val, pctEncodeSpaces) {
  return encodeURIComponent(val).
             replace(/%40/gi, '@').
             replace(/%3A/gi, ':').
             replace(/%24/g, '$').
             replace(/%2C/gi, ',').
             replace(/%3B/gi, ';').
             replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
}

var ngAttrPrefixes = ['ng-', 'data-ng-', 'ng:', 'x-ng-'];

function getNgAttribute(element, ngAttr) {
  var attr, i, ii = ngAttrPrefixes.length;
  for (i = 0; i < ii; ++i) {
    attr = ngAttrPrefixes[i] + ngAttr;
    if (isString(attr = element.getAttribute(attr))) {
      return attr;
    }
  }
  return null;
}

function allowAutoBootstrap(document) {
  var script = document.currentScript;

  if (!script) {
    // Support: IE 9-11 only
    // IE does not have `document.currentScript`
    return true;
  }

  // If the `currentScript` property has been clobbered just return false, since this indicates a probable attack
  if (!(script instanceof window.HTMLScriptElement || script instanceof window.SVGScriptElement)) {
    return false;
  }

  var attributes = script.attributes;
  var srcs = [attributes.getNamedItem('src'), attributes.getNamedItem('href'), attributes.getNamedItem('xlink:href')];

  return srcs.every(function(src) {
    if (!src) {
      return true;
    }
    if (!src.value) {
      return false;
    }

    var link = document.createElement('a');
    link.href = src.value;

    if (document.location.origin === link.origin) {
      // Same-origin resources are always allowed, even for non-whitelisted schemes.
      return true;
    }
    // Disabled bootstrapping unless angular.js was loaded from a known scheme used on the web.
    // This is to prevent angular.js bundled with browser extensions from being used to bypass the
    // content security policy in web pages and other browser extensions.
    switch (link.protocol) {
      case 'http:':
      case 'https:':
      case 'ftp:':
      case 'blob:':
      case 'file:':
      case 'data:':
        return true;
      default:
        return false;
    }
  });
}

// Cached as it has to run during loading so that document.currentScript is available.
var isAutoBootstrapAllowed = allowAutoBootstrap(window.document);

/**
 * @ngdoc directive
 * @name ngApp
 * @module ng
 *
 * @element ANY
 * @param {angular.Module} ngApp an optional application
 *   {@link angular.module module} name to load.
 * @param {boolean=} ngStrictDi if this attribute is present on the app element, the injector will be
 *   created in "strict-di" mode. This means that the application will fail to invoke functions which
 *   do not use explicit function annotation (and are thus unsuitable for minification), as described
 *   in {@link guide/di the Dependency Injection guide}, and useful debugging info will assist in
 *   tracking down the root of these bugs.
 *
 * @description
 *
 * Use this directive to **auto-bootstrap** an AngularJS application. The `ngApp` directive
 * designates the **root element** of the application and is typically placed near the root element
 * of the page - e.g. on the `<body>` or `<html>` tags.
 *
 * There are a few things to keep in mind when using `ngApp`:
 * - only one AngularJS application can be auto-bootstrapped per HTML document. The first `ngApp`
 *   found in the document will be used to define the root element to auto-bootstrap as an
 *   application. To run multiple applications in an HTML document you must manually bootstrap them using
 *   {@link angular.bootstrap} instead.
 * - AngularJS applications cannot be nested within each other.
 * - Do not use a directive that uses {@link ng.$compile#transclusion transclusion} on the same element as `ngApp`.
 *   This includes directives such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and
 *   {@link ngRoute.ngView `ngView`}.
 *   Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 *   causing animations to stop working and making the injector inaccessible from outside the app.
 *
 * You can specify an **AngularJS module** to be used as the root module for the application.  This
 * module will be loaded into the {@link auto.$injector} when the application is bootstrapped. It
 * should contain the application code needed or have dependencies on other modules that will
 * contain the code. See {@link angular.module} for more information.
 *
 * In the example below if the `ngApp` directive were not placed on the `html` element then the
 * document would not be compiled, the `AppController` would not be instantiated and the `{{ a+b }}`
 * would not be resolved to `3`.
 *
 * `ngApp` is the easiest, and most common way to bootstrap an application.
 *
 <example module="ngAppDemo" name="ng-app">
   <file name="index.html">
   <div ng-controller="ngAppDemoController">
     I can add: {{a}} + {{b}} =  {{ a+b }}
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppDemo', []).controller('ngAppDemoController', function($scope) {
     $scope.a = 1;
     $scope.b = 2;
   });
   </file>
 </example>
 *
 * Using `ngStrictDi`, you would see something like this:
 *
 <example ng-app-included="true" name="strict-di">
   <file name="index.html">
   <div ng-app="ngAppStrictDemo" ng-strict-di>
       <div ng-controller="GoodController1">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style (see
              script.js for details)
           </p>
       </div>

       <div ng-controller="GoodController2">
           Name: <input ng-model="name"><br />
           Hello, {{name}}!

           <p>This renders because the controller does not fail to
              instantiate, by using explicit annotation style
              (see script.js for details)
           </p>
       </div>

       <div ng-controller="BadController">
           I can add: {{a}} + {{b}} =  {{ a+b }}

           <p>The controller could not be instantiated, due to relying
              on automatic function annotations (which are disabled in
              strict mode). As such, the content of this section is not
              interpolated, and there should be an error in your web console.
           </p>
       </div>
   </div>
   </file>
   <file name="script.js">
   angular.module('ngAppStrictDemo', [])
     // BadController will fail to instantiate, due to relying on automatic function annotation,
     // rather than an explicit annotation
     .controller('BadController', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     })
     // Unlike BadController, GoodController1 and GoodController2 will not fail to be instantiated,
     // due to using explicit annotations using the array style and $inject property, respectively.
     .controller('GoodController1', ['$scope', function($scope) {
       $scope.a = 1;
       $scope.b = 2;
     }])
     .controller('GoodController2', GoodController2);
     function GoodController2($scope) {
       $scope.name = 'World';
     }
     GoodController2.$inject = ['$scope'];
   </file>
   <file name="style.css">
   div[ng-controller] {
       margin-bottom: 1em;
       -webkit-border-radius: 4px;
       border-radius: 4px;
       border: 1px solid;
       padding: .5em;
   }
   div[ng-controller^=Good] {
       border-color: #d6e9c6;
       background-color: #dff0d8;
       color: #3c763d;
   }
   div[ng-controller^=Bad] {
       border-color: #ebccd1;
       background-color: #f2dede;
       color: #a94442;
       margin-bottom: 0;
   }
   </file>
 </example>
 */
function angularInit(element, bootstrap) {
  var appElement,
      module,
      config = {};

  // The element `element` has priority over any other element.
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';

    if (!appElement && element.hasAttribute && element.hasAttribute(name)) {
      appElement = element;
      module = element.getAttribute(name);
    }
  });
  forEach(ngAttrPrefixes, function(prefix) {
    var name = prefix + 'app';
    var candidate;

    if (!appElement && (candidate = element.querySelector('[' + name.replace(':', '\\:') + ']'))) {
      appElement = candidate;
      module = candidate.getAttribute(name);
    }
  });
  if (appElement) {
    if (!isAutoBootstrapAllowed) {
      window.console.error('Angular: disabling automatic bootstrap. <script> protocol indicates ' +
          'an extension, document.location.href does not match.');
      return;
    }
    config.strictDi = getNgAttribute(appElement, 'strict-di') !== null;
    bootstrap(appElement, module ? [module] : [], config);
  }
}

/**
 * @ngdoc function
 * @name angular.bootstrap
 * @module ng
 * @description
 * Use this function to manually start up angular application.
 *
 * For more information, see the {@link guide/bootstrap Bootstrap guide}.
 *
 * Angular will detect if it has been loaded into the browser more than once and only allow the
 * first loaded script to be bootstrapped and will report a warning to the browser console for
 * each of the subsequent scripts. This prevents strange results in applications, where otherwise
 * multiple instances of Angular try to work on the DOM.
 *
 * <div class="alert alert-warning">
 * **Note:** Protractor based end-to-end tests cannot use this function to bootstrap manually.
 * They must use {@link ng.directive:ngApp ngApp}.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note:** Do not bootstrap the app on an element with a directive that uses {@link ng.$compile#transclusion transclusion},
 * such as {@link ng.ngIf `ngIf`}, {@link ng.ngInclude `ngInclude`} and {@link ngRoute.ngView `ngView`}.
 * Doing this misplaces the app {@link ng.$rootElement `$rootElement`} and the app's {@link auto.$injector injector},
 * causing animations to stop working and making the injector inaccessible from outside the app.
 * </div>
 *
 * ```html
 * <!doctype html>
 * <html>
 * <body>
 * <div ng-controller="WelcomeController">
 *   {{greeting}}
 * </div>
 *
 * <script src="angular.js"></script>
 * <script>
 *   var app = angular.module('demo', [])
 *   .controller('WelcomeController', function($scope) {
 *       $scope.greeting = 'Welcome!';
 *   });
 *   angular.bootstrap(document, ['demo']);
 * </script>
 * </body>
 * </html>
 * ```
 *
 * @param {DOMElement} element DOM element which is the root of angular application.
 * @param {Array<String|Function|Array>=} modules an array of modules to load into the application.
 *     Each item in the array should be the name of a predefined module or a (DI annotated)
 *     function that will be invoked by the injector as a `config` block.
 *     See: {@link angular.module modules}
 * @param {Object=} config an object for defining configuration options for the application. The
 *     following keys are supported:
 *
 * * `strictDi` - disable automatic function annotation for the application. This is meant to
 *   assist in finding bugs which break minified code. Defaults to `false`.
 *
 * @returns {auto.$injector} Returns the newly created injector for this app.
 */
function bootstrap(element, modules, config) {
  if (!isObject(config)) config = {};
  var defaultConfig = {
    strictDi: false
  };
  config = extend(defaultConfig, config);
  var doBootstrap = function() {
    element = jqLite(element);

    if (element.injector()) {
      var tag = (element[0] === window.document) ? 'document' : startingTag(element);
      // Encode angle brackets to prevent input from being sanitized to empty string #8683.
      throw ngMinErr(
          'btstrpd',
          'App already bootstrapped with this element \'{0}\'',
          tag.replace(/</,'&lt;').replace(/>/,'&gt;'));
    }

    modules = modules || [];
    modules.unshift(['$provide', function($provide) {
      $provide.value('$rootElement', element);
    }]);

    if (config.debugInfoEnabled) {
      // Pushing so that this overrides `debugInfoEnabled` setting defined in user's `modules`.
      modules.push(['$compileProvider', function($compileProvider) {
        $compileProvider.debugInfoEnabled(true);
      }]);
    }

    modules.unshift('ng');
    var injector = createInjector(modules, config.strictDi);
    injector.invoke(['$rootScope', '$rootElement', '$compile', '$injector',
       function bootstrapApply(scope, element, compile, injector) {
        scope.$apply(function() {
          element.data('$injector', injector);
          compile(element)(scope);
        });
      }]
    );
    return injector;
  };

  var NG_ENABLE_DEBUG_INFO = /^NG_ENABLE_DEBUG_INFO!/;
  var NG_DEFER_BOOTSTRAP = /^NG_DEFER_BOOTSTRAP!/;

  if (window && NG_ENABLE_DEBUG_INFO.test(window.name)) {
    config.debugInfoEnabled = true;
    window.name = window.name.replace(NG_ENABLE_DEBUG_INFO, '');
  }

  if (window && !NG_DEFER_BOOTSTRAP.test(window.name)) {
    return doBootstrap();
  }

  window.name = window.name.replace(NG_DEFER_BOOTSTRAP, '');
  angular.resumeBootstrap = function(extraModules) {
    forEach(extraModules, function(module) {
      modules.push(module);
    });
    return doBootstrap();
  };

  if (isFunction(angular.resumeDeferredBootstrap)) {
    angular.resumeDeferredBootstrap();
  }
}

/**
 * @ngdoc function
 * @name angular.reloadWithDebugInfo
 * @module ng
 * @description
 * Use this function to reload the current application with debug information turned on.
 * This takes precedence over a call to `$compileProvider.debugInfoEnabled(false)`.
 *
 * See {@link ng.$compileProvider#debugInfoEnabled} for more.
 */
function reloadWithDebugInfo() {
  window.name = 'NG_ENABLE_DEBUG_INFO!' + window.name;
  window.location.reload();
}

/**
 * @name angular.getTestability
 * @module ng
 * @description
 * Get the testability service for the instance of Angular on the given
 * element.
 * @param {DOMElement} element DOM element which is the root of angular application.
 */
function getTestability(rootElement) {
  var injector = angular.element(rootElement).injector();
  if (!injector) {
    throw ngMinErr('test',
      'no injector found for element argument to getTestability');
  }
  return injector.get('$$testability');
}

var SNAKE_CASE_REGEXP = /[A-Z]/g;
function snake_case(name, separator) {
  separator = separator || '_';
  return name.replace(SNAKE_CASE_REGEXP, function(letter, pos) {
    return (pos ? separator : '') + letter.toLowerCase();
  });
}

var bindJQueryFired = false;
function bindJQuery() {
  var originalCleanData;

  if (bindJQueryFired) {
    return;
  }

  // bind to jQuery if present;
  var jqName = jq();
  jQuery = isUndefined(jqName) ? window.jQuery :   // use jQuery (if present)
           !jqName             ? undefined     :   // use jqLite
                                 window[jqName];   // use jQuery specified by `ngJq`

  // Use jQuery if it exists with proper functionality, otherwise default to us.
  // Angular 1.2+ requires jQuery 1.7+ for on()/off() support.
  // Angular 1.3+ technically requires at least jQuery 2.1+ but it may work with older
  // versions. It will not work for sure with jQuery <1.7, though.
  if (jQuery && jQuery.fn.on) {
    jqLite = jQuery;
    extend(jQuery.fn, {
      scope: JQLitePrototype.scope,
      isolateScope: JQLitePrototype.isolateScope,
      controller: /** @type {?} */ (JQLitePrototype).controller,
      injector: JQLitePrototype.injector,
      inheritedData: JQLitePrototype.inheritedData
    });

    // All nodes removed from the DOM via various jQuery APIs like .remove()
    // are passed through jQuery.cleanData. Monkey-patch this method to fire
    // the $destroy event on all removed nodes.
    originalCleanData = jQuery.cleanData;
    jQuery.cleanData = function(elems) {
      var events;
      for (var i = 0, elem; (elem = elems[i]) != null; i++) {
        events = jQuery._data(elem, 'events');
        if (events && events.$destroy) {
          jQuery(elem).triggerHandler('$destroy');
        }
      }
      originalCleanData(elems);
    };
  } else {
    jqLite = JQLite;
  }

  angular.element = jqLite;

  // Prevent double-proxying.
  bindJQueryFired = true;
}

/**
 * throw error if the argument is falsy.
 */
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function assertArgFn(arg, name, acceptArrayAnnotation) {
  if (acceptArrayAnnotation && isArray(arg)) {
      arg = arg[arg.length - 1];
  }

  assertArg(isFunction(arg), name, 'not a function, got ' +
      (arg && typeof arg === 'object' ? arg.constructor.name || 'Object' : typeof arg));
  return arg;
}

/**
 * throw error if the name given is hasOwnProperty
 * @param  {String} name    the name to test
 * @param  {String} context the context in which the name is used, such as module or directive
 */
function assertNotHasOwnProperty(name, context) {
  if (name === 'hasOwnProperty') {
    throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
  }
}

/**
 * Return the value accessible from the object by path. Any undefined traversals are ignored
 * @param {Object} obj starting object
 * @param {String} path path to traverse
 * @param {boolean} [bindFnToScope=true]
 * @returns {Object} value as accessible by path
 */
//TODO(misko): this function needs to be removed
function getter(obj, path, bindFnToScope) {
  if (!path) return obj;
  var keys = path.split('.');
  var key;
  var lastInstance = obj;
  var len = keys.length;

  for (var i = 0; i < len; i++) {
    key = keys[i];
    if (obj) {
      obj = (lastInstance = obj)[key];
    }
  }
  if (!bindFnToScope && isFunction(obj)) {
    return bind(lastInstance, obj);
  }
  return obj;
}

/**
 * Return the DOM siblings between the first and last node in the given array.
 * @param {Array} array like object
 * @returns {Array} the inputted object or a jqLite collection containing the nodes
 */
function getBlockNodes(nodes) {
  // TODO(perf): update `nodes` instead of creating a new object?
  var node = nodes[0];
  var endNode = nodes[nodes.length - 1];
  var blockNodes;

  for (var i = 1; node !== endNode && (node = node.nextSibling); i++) {
    if (blockNodes || nodes[i] !== node) {
      if (!blockNodes) {
        blockNodes = jqLite(slice.call(nodes, 0, i));
      }
      blockNodes.push(node);
    }
  }

  return blockNodes || nodes;
}


/**
 * Creates a new object without a prototype. This object is useful for lookup without having to
 * guard against prototypically inherited properties via hasOwnProperty.
 *
 * Related micro-benchmarks:
 * - http://jsperf.com/object-create2
 * - http://jsperf.com/proto-map-lookup/2
 * - http://jsperf.com/for-in-vs-object-keys2
 *
 * @returns {Object}
 */
function createMap() {
  return Object.create(null);
}

function stringify(value) {
  if (value == null) { // null || undefined
    return '';
  }
  switch (typeof value) {
    case 'string':
      break;
    case 'number':
      value = '' + value;
      break;
    default:
      if (hasCustomToString(value) && !isArray(value) && !isDate(value)) {
        value = value.toString();
      } else {
        value = toJson(value);
      }
  }

  return value;
}

var NODE_TYPE_ELEMENT = 1;
var NODE_TYPE_ATTRIBUTE = 2;
var NODE_TYPE_TEXT = 3;
var NODE_TYPE_COMMENT = 8;
var NODE_TYPE_DOCUMENT = 9;
var NODE_TYPE_DOCUMENT_FRAGMENT = 11;

/**
 * @ngdoc type
 * @name angular.Module
 * @module ng
 * @description
 *
 * Interface for configuring angular {@link angular.module modules}.
 */

function setupModuleLoader(window) {

  var $injectorMinErr = minErr('$injector');
  var ngMinErr = minErr('ng');

  function ensure(obj, name, factory) {
    return obj[name] || (obj[name] = factory());
  }

  var angular = ensure(window, 'angular', Object);

  // We need to expose `angular.$$minErr` to modules such as `ngResource` that reference it during bootstrap
  angular.$$minErr = angular.$$minErr || minErr;

  return ensure(angular, 'module', function() {
    /** @type {Object.<string, angular.Module>} */
    var modules = {};

    /**
     * @ngdoc function
     * @name angular.module
     * @module ng
     * @description
     *
     * The `angular.module` is a global place for creating, registering and retrieving Angular
     * modules.
     * All modules (angular core or 3rd party) that should be available to an application must be
     * registered using this mechanism.
     *
     * Passing one argument retrieves an existing {@link angular.Module},
     * whereas passing more than one argument creates a new {@link angular.Module}
     *
     *
     * # Module
     *
     * A module is a collection of services, directives, controllers, filters, and configuration information.
     * `angular.module` is used to configure the {@link auto.$injector $injector}.
     *
     * ```js
     * // Create a new module
     * var myModule = angular.module('myModule', []);
     *
     * // register a new service
     * myModule.value('appName', 'MyCoolApp');
     *
     * // configure existing services inside initialization blocks.
     * myModule.config(['$locationProvider', function($locationProvider) {
     *   // Configure existing providers
     *   $locationProvider.hashPrefix('!');
     * }]);
     * ```
     *
     * Then you can create an injector and load your modules like this:
     *
     * ```js
     * var injector = angular.injector(['ng', 'myModule'])
     * ```
     *
     * However it's more likely that you'll just use
     * {@link ng.directive:ngApp ngApp} or
     * {@link angular.bootstrap} to simplify this process for you.
     *
     * @param {!string} name The name of the module to create or retrieve.
     * @param {!Array.<string>=} requires If specified then new module is being created. If
     *        unspecified then the module is being retrieved for further configuration.
     * @param {Function=} configFn Optional configuration function for the module. Same as
     *        {@link angular.Module#config Module#config()}.
     * @returns {angular.Module} new module with the {@link angular.Module} api.
     */
    return function module(name, requires, configFn) {

      var info = {};

      var assertNotHasOwnProperty = function(name, context) {
        if (name === 'hasOwnProperty') {
          throw ngMinErr('badname', 'hasOwnProperty is not a valid {0} name', context);
        }
      };

      assertNotHasOwnProperty(name, 'module');
      if (requires && modules.hasOwnProperty(name)) {
        modules[name] = null;
      }
      return ensure(modules, name, function() {
        if (!requires) {
          throw $injectorMinErr('nomod', 'Module \'{0}\' is not available! You either misspelled ' +
             'the module name or forgot to load it. If registering a module ensure that you ' +
             'specify the dependencies as the second argument.', name);
        }

        /** @type {!Array.<Array.<*>>} */
        var invokeQueue = [];

        /** @type {!Array.<Function>} */
        var configBlocks = [];

        /** @type {!Array.<Function>} */
        var runBlocks = [];

        var config = invokeLater('$injector', 'invoke', 'push', configBlocks);

        /** @type {angular.Module} */
        var moduleInstance = {
          // Private state
          _invokeQueue: invokeQueue,
          _configBlocks: configBlocks,
          _runBlocks: runBlocks,

          /**
           * @ngdoc method
           * @name angular.Module#info
           * @module ng
           *
           * @param {Object=} info Information about the module
           * @returns {Object|Module} The current info object for this module if called as a getter,
           *                          or `this` if called as a setter.
           *
           * @description
           * Read and write custom information about this module.
           * For example you could put the version of the module in here.
           *
           * ```js
           * angular.module('myModule', []).info({ version: '1.0.0' });
           * ```
           *
           * The version could then be read back out by accessing the module elsewhere:
           *
           * ```
           * var version = angular.module('myModule').info().version;
           * ```
           *
           * You can also retrieve this information during runtime via the
           * {@link $injector#modules `$injector.modules`} property:
           *
           * ```js
           * var version = $injector.modules['myModule'].info().version;
           * ```
           */
          info: function(value) {
            if (isDefined(value)) {
              if (!isObject(value)) throw ngMinErr('aobj', 'Argument \'{0}\' must be an object', 'value');
              info = value;
              return this;
            }
            return info;
          },

          /**
           * @ngdoc property
           * @name angular.Module#requires
           * @module ng
           *
           * @description
           * Holds the list of modules which the injector will load before the current module is
           * loaded.
           */
          requires: requires,

          /**
           * @ngdoc property
           * @name angular.Module#name
           * @module ng
           *
           * @description
           * Name of the module.
           */
          name: name,


          /**
           * @ngdoc method
           * @name angular.Module#provider
           * @module ng
           * @param {string} name service name
           * @param {Function} providerType Construction function for creating new instance of the
           *                                service.
           * @description
           * See {@link auto.$provide#provider $provide.provider()}.
           */
          provider: invokeLaterAndSetModuleName('$provide', 'provider'),

          /**
           * @ngdoc method
           * @name angular.Module#factory
           * @module ng
           * @param {string} name service name
           * @param {Function} providerFunction Function for creating new instance of the service.
           * @description
           * See {@link auto.$provide#factory $provide.factory()}.
           */
          factory: invokeLaterAndSetModuleName('$provide', 'factory'),

          /**
           * @ngdoc method
           * @name angular.Module#service
           * @module ng
           * @param {string} name service name
           * @param {Function} constructor A constructor function that will be instantiated.
           * @description
           * See {@link auto.$provide#service $provide.service()}.
           */
          service: invokeLaterAndSetModuleName('$provide', 'service'),

          /**
           * @ngdoc method
           * @name angular.Module#value
           * @module ng
           * @param {string} name service name
           * @param {*} object Service instance object.
           * @description
           * See {@link auto.$provide#value $provide.value()}.
           */
          value: invokeLater('$provide', 'value'),

          /**
           * @ngdoc method
           * @name angular.Module#constant
           * @module ng
           * @param {string} name constant name
           * @param {*} object Constant value.
           * @description
           * Because the constants are fixed, they get applied before other provide methods.
           * See {@link auto.$provide#constant $provide.constant()}.
           */
          constant: invokeLater('$provide', 'constant', 'unshift'),

           /**
           * @ngdoc method
           * @name angular.Module#decorator
           * @module ng
           * @param {string} name The name of the service to decorate.
           * @param {Function} decorFn This function will be invoked when the service needs to be
           *                           instantiated and should return the decorated service instance.
           * @description
           * See {@link auto.$provide#decorator $provide.decorator()}.
           */
          decorator: invokeLaterAndSetModuleName('$provide', 'decorator', configBlocks),

          /**
           * @ngdoc method
           * @name angular.Module#animation
           * @module ng
           * @param {string} name animation name
           * @param {Function} animationFactory Factory function for creating new instance of an
           *                                    animation.
           * @description
           *
           * **NOTE**: animations take effect only if the **ngAnimate** module is loaded.
           *
           *
           * Defines an animation hook that can be later used with
           * {@link $animate $animate} service and directives that use this service.
           *
           * ```js
           * module.animation('.animation-name', function($inject1, $inject2) {
           *   return {
           *     eventName : function(element, done) {
           *       //code to run the animation
           *       //once complete, then run done()
           *       return function cancellationFunction(element) {
           *         //code to cancel the animation
           *       }
           *     }
           *   }
           * })
           * ```
           *
           * See {@link ng.$animateProvider#register $animateProvider.register()} and
           * {@link ngAnimate ngAnimate module} for more information.
           */
          animation: invokeLaterAndSetModuleName('$animateProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#filter
           * @module ng
           * @param {string} name Filter name - this must be a valid angular expression identifier
           * @param {Function} filterFactory Factory function for creating new instance of filter.
           * @description
           * See {@link ng.$filterProvider#register $filterProvider.register()}.
           *
           * <div class="alert alert-warning">
           * **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
           * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
           * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
           * (`myapp_subsection_filterx`).
           * </div>
           */
          filter: invokeLaterAndSetModuleName('$filterProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#controller
           * @module ng
           * @param {string|Object} name Controller name, or an object map of controllers where the
           *    keys are the names and the values are the constructors.
           * @param {Function} constructor Controller constructor function.
           * @description
           * See {@link ng.$controllerProvider#register $controllerProvider.register()}.
           */
          controller: invokeLaterAndSetModuleName('$controllerProvider', 'register'),

          /**
           * @ngdoc method
           * @name angular.Module#directive
           * @module ng
           * @param {string|Object} name Directive name, or an object map of directives where the
           *    keys are the names and the values are the factories.
           * @param {Function} directiveFactory Factory function for creating new instance of
           * directives.
           * @description
           * See {@link ng.$compileProvider#directive $compileProvider.directive()}.
           */
          directive: invokeLaterAndSetModuleName('$compileProvider', 'directive'),

          /**
           * @ngdoc method
           * @name angular.Module#component
           * @module ng
           * @param {string} name Name of the component in camel-case (i.e. myComp which will match as my-comp)
           * @param {Object} options Component definition object (a simplified
           *    {@link ng.$compile#directive-definition-object directive definition object})
           *
           * @description
           * See {@link ng.$compileProvider#component $compileProvider.component()}.
           */
          component: invokeLaterAndSetModuleName('$compileProvider', 'component'),

          /**
           * @ngdoc method
           * @name angular.Module#config
           * @module ng
           * @param {Function} configFn Execute this function on module load. Useful for service
           *    configuration.
           * @description
           * Use this method to register work which needs to be performed on module loading.
           * For more about how to configure services, see
           * {@link providers#provider-recipe Provider Recipe}.
           */
          config: config,

          /**
           * @ngdoc method
           * @name angular.Module#run
           * @module ng
           * @param {Function} initializationFn Execute this function after injector creation.
           *    Useful for application initialization.
           * @description
           * Use this method to register work which should be performed when the injector is done
           * loading all modules.
           */
          run: function(block) {
            runBlocks.push(block);
            return this;
          }
        };

        if (configFn) {
          config(configFn);
        }

        return moduleInstance;

        /**
         * @param {string} provider
         * @param {string} method
         * @param {String=} insertMethod
         * @returns {angular.Module}
         */
        function invokeLater(provider, method, insertMethod, queue) {
          if (!queue) queue = invokeQueue;
          return function() {
            queue[insertMethod || 'push']([provider, method, arguments]);
            return moduleInstance;
          };
        }

        /**
         * @param {string} provider
         * @param {string} method
         * @returns {angular.Module}
         */
        function invokeLaterAndSetModuleName(provider, method, queue) {
          if (!queue) queue = invokeQueue;
          return function(recipeName, factoryFunction) {
            if (factoryFunction && isFunction(factoryFunction)) factoryFunction.$$moduleName = name;
            queue.push([provider, method, arguments]);
            return moduleInstance;
          };
        }
      });
    };
  });

}

/* global shallowCopy: true */

/**
 * Creates a shallow copy of an object, an array or a primitive.
 *
 * Assumes that there are no proto properties for objects.
 */
function shallowCopy(src, dst) {
  if (isArray(src)) {
    dst = dst || [];

    for (var i = 0, ii = src.length; i < ii; i++) {
      dst[i] = src[i];
    }
  } else if (isObject(src)) {
    dst = dst || {};

    for (var key in src) {
      if (!(key.charAt(0) === '$' && key.charAt(1) === '$')) {
        dst[key] = src[key];
      }
    }
  }

  return dst || src;
}

/* exported toDebugString */

function serializeObject(obj, maxDepth) {
  var seen = [];

  // There is no direct way to stringify object until reaching a specific depth
  // and a very deep object can cause a performance issue, so we copy the object
  // based on this specific depth and then stringify it.
  if (isValidObjectMaxDepth(maxDepth)) {
    // This file is also included in `angular-loader`, so `copy()` might not always be available in
    // the closure. Therefore, it is lazily retrieved as `angular.copy()` when needed.
    obj = angular.copy(obj, null, maxDepth);
  }
  return JSON.stringify(obj, function(key, val) {
    val = toJsonReplacer(key, val);
    if (isObject(val)) {

      if (seen.indexOf(val) >= 0) return '...';

      seen.push(val);
    }
    return val;
  });
}

function toDebugString(obj, maxDepth) {
  if (typeof obj === 'function') {
    return obj.toString().replace(/ \{[\s\S]*$/, '');
  } else if (isUndefined(obj)) {
    return 'undefined';
  } else if (typeof obj !== 'string') {
    return serializeObject(obj, maxDepth);
  }
  return obj;
}

/* global angularModule: true,
  version: true,

  $CompileProvider,

  htmlAnchorDirective,
  inputDirective,
  inputDirective,
  formDirective,
  scriptDirective,
  selectDirective,
  optionDirective,
  ngBindDirective,
  ngBindHtmlDirective,
  ngBindTemplateDirective,
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective,
  ngCloakDirective,
  ngControllerDirective,
  ngFormDirective,
  ngHideDirective,
  ngIfDirective,
  ngIncludeDirective,
  ngIncludeFillContentDirective,
  ngInitDirective,
  ngNonBindableDirective,
  ngPluralizeDirective,
  ngRepeatDirective,
  ngShowDirective,
  ngStyleDirective,
  ngSwitchDirective,
  ngSwitchWhenDirective,
  ngSwitchDefaultDirective,
  ngOptionsDirective,
  ngTranscludeDirective,
  ngModelDirective,
  ngListDirective,
  ngChangeDirective,
  patternDirective,
  patternDirective,
  requiredDirective,
  requiredDirective,
  minlengthDirective,
  minlengthDirective,
  maxlengthDirective,
  maxlengthDirective,
  ngValueDirective,
  ngModelOptionsDirective,
  ngAttributeAliasDirectives,
  ngEventDirectives,

  $AnchorScrollProvider,
  $AnimateProvider,
  $CoreAnimateCssProvider,
  $$CoreAnimateJsProvider,
  $$CoreAnimateQueueProvider,
  $$AnimateRunnerFactoryProvider,
  $$AnimateAsyncRunFactoryProvider,
  $BrowserProvider,
  $CacheFactoryProvider,
  $ControllerProvider,
  $DateProvider,
  $DocumentProvider,
  $$IsDocumentHiddenProvider,
  $ExceptionHandlerProvider,
  $FilterProvider,
  $$ForceReflowProvider,
  $InterpolateProvider,
  $IntervalProvider,
  $HttpProvider,
  $HttpParamSerializerProvider,
  $HttpParamSerializerJQLikeProvider,
  $HttpBackendProvider,
  $xhrFactoryProvider,
  $jsonpCallbacksProvider,
  $LocationProvider,
  $LogProvider,
  $$MapProvider,
  $ParseProvider,
  $RootScopeProvider,
  $QProvider,
  $$QProvider,
  $$SanitizeUriProvider,
  $SceProvider,
  $SceDelegateProvider,
  $SnifferProvider,
  $TemplateCacheProvider,
  $TemplateRequestProvider,
  $$TestabilityProvider,
  $TimeoutProvider,
  $$RAFProvider,
  $WindowProvider,
  $$jqLiteProvider,
  $$CookieReaderProvider
*/


/**
 * @ngdoc object
 * @name angular.version
 * @module ng
 * @description
 * An object that contains information about the current AngularJS version.
 *
 * This object has the following properties:
 *
 * - `full` – `{string}` – Full version string, such as "0.9.18".
 * - `major` – `{number}` – Major version number, such as "0".
 * - `minor` – `{number}` – Minor version number, such as "9".
 * - `dot` – `{number}` – Dot version number, such as "18".
 * - `codeName` – `{string}` – Code name of the release, such as "jiggling-armfat".
 */
var version = {
  // These placeholder strings will be replaced by grunt's `build` task.
  // They need to be double- or single-quoted.
  full: '1.6.6',
  major: 1,
  minor: 6,
  dot: 6,
  codeName: 'interdimensional-cable'
};


function publishExternalAPI(angular) {
  extend(angular, {
    'errorHandlingConfig': errorHandlingConfig,
    'bootstrap': bootstrap,
    'copy': copy,
    'extend': extend,
    'merge': merge,
    'equals': equals,
    'element': jqLite,
    'forEach': forEach,
    'injector': createInjector,
    'noop': noop,
    'bind': bind,
    'toJson': toJson,
    'fromJson': fromJson,
    'identity': identity,
    'isUndefined': isUndefined,
    'isDefined': isDefined,
    'isString': isString,
    'isFunction': isFunction,
    'isObject': isObject,
    'isNumber': isNumber,
    'isElement': isElement,
    'isArray': isArray,
    'version': version,
    'isDate': isDate,
    'lowercase': lowercase,
    'uppercase': uppercase,
    'callbacks': {$$counter: 0},
    'getTestability': getTestability,
    'reloadWithDebugInfo': reloadWithDebugInfo,
    '$$minErr': minErr,
    '$$csp': csp,
    '$$encodeUriSegment': encodeUriSegment,
    '$$encodeUriQuery': encodeUriQuery,
    '$$stringify': stringify
  });

  angularModule = setupModuleLoader(window);

  angularModule('ng', ['ngLocale'], ['$provide',
    function ngModule($provide) {
      // $$sanitizeUriProvider needs to be before $compileProvider as it is used by it.
      $provide.provider({
        $$sanitizeUri: $$SanitizeUriProvider
      });
      $provide.provider('$compile', $CompileProvider).
        directive({
            a: htmlAnchorDirective,
            input: inputDirective,
            textarea: inputDirective,
            form: formDirective,
            script: scriptDirective,
            select: selectDirective,
            option: optionDirective,
            ngBind: ngBindDirective,
            ngBindHtml: ngBindHtmlDirective,
            ngBindTemplate: ngBindTemplateDirective,
            ngClass: ngClassDirective,
            ngClassEven: ngClassEvenDirective,
            ngClassOdd: ngClassOddDirective,
            ngCloak: ngCloakDirective,
            ngController: ngControllerDirective,
            ngForm: ngFormDirective,
            ngHide: ngHideDirective,
            ngIf: ngIfDirective,
            ngInclude: ngIncludeDirective,
            ngInit: ngInitDirective,
            ngNonBindable: ngNonBindableDirective,
            ngPluralize: ngPluralizeDirective,
            ngRepeat: ngRepeatDirective,
            ngShow: ngShowDirective,
            ngStyle: ngStyleDirective,
            ngSwitch: ngSwitchDirective,
            ngSwitchWhen: ngSwitchWhenDirective,
            ngSwitchDefault: ngSwitchDefaultDirective,
            ngOptions: ngOptionsDirective,
            ngTransclude: ngTranscludeDirective,
            ngModel: ngModelDirective,
            ngList: ngListDirective,
            ngChange: ngChangeDirective,
            pattern: patternDirective,
            ngPattern: patternDirective,
            required: requiredDirective,
            ngRequired: requiredDirective,
            minlength: minlengthDirective,
            ngMinlength: minlengthDirective,
            maxlength: maxlengthDirective,
            ngMaxlength: maxlengthDirective,
            ngValue: ngValueDirective,
            ngModelOptions: ngModelOptionsDirective
        }).
        directive({
          ngInclude: ngIncludeFillContentDirective
        }).
        directive(ngAttributeAliasDirectives).
        directive(ngEventDirectives);
      $provide.provider({
        $anchorScroll: $AnchorScrollProvider,
        $animate: $AnimateProvider,
        $animateCss: $CoreAnimateCssProvider,
        $$animateJs: $$CoreAnimateJsProvider,
        $$animateQueue: $$CoreAnimateQueueProvider,
        $$AnimateRunner: $$AnimateRunnerFactoryProvider,
        $$animateAsyncRun: $$AnimateAsyncRunFactoryProvider,
        $browser: $BrowserProvider,
        $cacheFactory: $CacheFactoryProvider,
        $controller: $ControllerProvider,
        $document: $DocumentProvider,
        $$isDocumentHidden: $$IsDocumentHiddenProvider,
        $exceptionHandler: $ExceptionHandlerProvider,
        $filter: $FilterProvider,
        $$forceReflow: $$ForceReflowProvider,
        $interpolate: $InterpolateProvider,
        $interval: $IntervalProvider,
        $http: $HttpProvider,
        $httpParamSerializer: $HttpParamSerializerProvider,
        $httpParamSerializerJQLike: $HttpParamSerializerJQLikeProvider,
        $httpBackend: $HttpBackendProvider,
        $xhrFactory: $xhrFactoryProvider,
        $jsonpCallbacks: $jsonpCallbacksProvider,
        $location: $LocationProvider,
        $log: $LogProvider,
        $parse: $ParseProvider,
        $rootScope: $RootScopeProvider,
        $q: $QProvider,
        $$q: $$QProvider,
        $sce: $SceProvider,
        $sceDelegate: $SceDelegateProvider,
        $sniffer: $SnifferProvider,
        $templateCache: $TemplateCacheProvider,
        $templateRequest: $TemplateRequestProvider,
        $$testability: $$TestabilityProvider,
        $timeout: $TimeoutProvider,
        $window: $WindowProvider,
        $$rAF: $$RAFProvider,
        $$jqLite: $$jqLiteProvider,
        $$Map: $$MapProvider,
        $$cookieReader: $$CookieReaderProvider
      });
    }
  ])
  .info({ angularVersion: '1.6.6' });
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* global
  JQLitePrototype: true,
  BOOLEAN_ATTR: true,
  ALIASED_ATTR: true
*/

//////////////////////////////////
//JQLite
//////////////////////////////////

/**
 * @ngdoc function
 * @name angular.element
 * @module ng
 * @kind function
 *
 * @description
 * Wraps a raw DOM element or HTML string as a [jQuery](http://jquery.com) element.
 *
 * If jQuery is available, `angular.element` is an alias for the
 * [jQuery](http://api.jquery.com/jQuery/) function. If jQuery is not available, `angular.element`
 * delegates to Angular's built-in subset of jQuery, called "jQuery lite" or **jqLite**.
 *
 * jqLite is a tiny, API-compatible subset of jQuery that allows
 * Angular to manipulate the DOM in a cross-browser compatible way. jqLite implements only the most
 * commonly needed functionality with the goal of having a very small footprint.
 *
 * To use `jQuery`, simply ensure it is loaded before the `angular.js` file. You can also use the
 * {@link ngJq `ngJq`} directive to specify that jqlite should be used over jQuery, or to use a
 * specific version of jQuery if multiple versions exist on the page.
 *
 * <div class="alert alert-info">**Note:** All element references in Angular are always wrapped with jQuery or
 * jqLite (such as the element argument in a directive's compile / link function). They are never raw DOM references.</div>
 *
 * <div class="alert alert-warning">**Note:** Keep in mind that this function will not find elements
 * by tag name / CSS selector. For lookups by tag name, try instead `angular.element(document).find(...)`
 * or `$document.find()`, or use the standard DOM APIs, e.g. `document.querySelectorAll()`.</div>
 *
 * ## Angular's jqLite
 * jqLite provides only the following jQuery methods:
 *
 * - [`addClass()`](http://api.jquery.com/addClass/) - Does not support a function as first argument
 * - [`after()`](http://api.jquery.com/after/)
 * - [`append()`](http://api.jquery.com/append/)
 * - [`attr()`](http://api.jquery.com/attr/) - Does not support functions as parameters
 * - [`bind()`](http://api.jquery.com/bind/) (_deprecated_, use [`on()`](http://api.jquery.com/on/)) - Does not support namespaces, selectors or eventData
 * - [`children()`](http://api.jquery.com/children/) - Does not support selectors
 * - [`clone()`](http://api.jquery.com/clone/)
 * - [`contents()`](http://api.jquery.com/contents/)
 * - [`css()`](http://api.jquery.com/css/) - Only retrieves inline-styles, does not call `getComputedStyle()`.
 *   As a setter, does not convert numbers to strings or append 'px', and also does not have automatic property prefixing.
 * - [`data()`](http://api.jquery.com/data/)
 * - [`detach()`](http://api.jquery.com/detach/)
 * - [`empty()`](http://api.jquery.com/empty/)
 * - [`eq()`](http://api.jquery.com/eq/)
 * - [`find()`](http://api.jquery.com/find/) - Limited to lookups by tag name
 * - [`hasClass()`](http://api.jquery.com/hasClass/)
 * - [`html()`](http://api.jquery.com/html/)
 * - [`next()`](http://api.jquery.com/next/) - Does not support selectors
 * - [`on()`](http://api.jquery.com/on/) - Does not support namespaces, selectors or eventData
 * - [`off()`](http://api.jquery.com/off/) - Does not support namespaces, selectors or event object as parameter
 * - [`one()`](http://api.jquery.com/one/) - Does not support namespaces or selectors
 * - [`parent()`](http://api.jquery.com/parent/) - Does not support selectors
 * - [`prepend()`](http://api.jquery.com/prepend/)
 * - [`prop()`](http://api.jquery.com/prop/)
 * - [`ready()`](http://api.jquery.com/ready/) (_deprecated_, use `angular.element(callback)` instead of `angular.element(document).ready(callback)`)
 * - [`remove()`](http://api.jquery.com/remove/)
 * - [`removeAttr()`](http://api.jquery.com/removeAttr/) - Does not support multiple attributes
 * - [`removeClass()`](http://api.jquery.com/removeClass/) - Does not support a function as first argument
 * - [`removeData()`](http://api.jquery.com/removeData/)
 * - [`replaceWith()`](http://api.jquery.com/replaceWith/)
 * - [`text()`](http://api.jquery.com/text/)
 * - [`toggleClass()`](http://api.jquery.com/toggleClass/) - Does not support a function as first argument
 * - [`triggerHandler()`](http://api.jquery.com/triggerHandler/) - Passes a dummy event object to handlers
 * - [`unbind()`](http://api.jquery.com/unbind/) (_deprecated_, use [`off()`](http://api.jquery.com/off/)) - Does not support namespaces or event object as parameter
 * - [`val()`](http://api.jquery.com/val/)
 * - [`wrap()`](http://api.jquery.com/wrap/)
 *
 * ## jQuery/jqLite Extras
 * Angular also provides the following additional methods and events to both jQuery and jqLite:
 *
 * ### Events
 * - `$destroy` - AngularJS intercepts all jqLite/jQuery's DOM destruction apis and fires this event
 *    on all DOM nodes being removed.  This can be used to clean up any 3rd party bindings to the DOM
 *    element before it is removed.
 *
 * ### Methods
 * - `controller(name)` - retrieves the controller of the current element or its parent. By default
 *   retrieves controller associated with the `ngController` directive. If `name` is provided as
 *   camelCase directive name, then the controller for this directive will be retrieved (e.g.
 *   `'ngModel'`).
 * - `injector()` - retrieves the injector of the current element or its parent.
 * - `scope()` - retrieves the {@link ng.$rootScope.Scope scope} of the current
 *   element or its parent. Requires {@link guide/production#disabling-debug-data Debug Data} to
 *   be enabled.
 * - `isolateScope()` - retrieves an isolate {@link ng.$rootScope.Scope scope} if one is attached directly to the
 *   current element. This getter should be used only on elements that contain a directive which starts a new isolate
 *   scope. Calling `scope()` on this element always returns the original non-isolate scope.
 *   Requires {@link guide/production#disabling-debug-data Debug Data} to be enabled.
 * - `inheritedData()` - same as `data()`, but walks up the DOM until a value is found or the top
 *   parent element is reached.
 *
 * @knownIssue You cannot spy on `angular.element` if you are using Jasmine version 1.x. See
 * https://github.com/angular/angular.js/issues/14251 for more information.
 *
 * @param {string|DOMElement} element HTML string or DOMElement to be wrapped into jQuery.
 * @returns {Object} jQuery object.
 */

JQLite.expando = 'ng339';

var jqCache = JQLite.cache = {},
    jqId = 1;

/*
 * !!! This is an undocumented "private" function !!!
 */
JQLite._data = function(node) {
  //jQuery always returns an object on cache miss
  return this.cache[node[this.expando]] || {};
};

function jqNextId() { return ++jqId; }


var DASH_LOWERCASE_REGEXP = /-([a-z])/g;
var MS_HACK_REGEXP = /^-ms-/;
var MOUSE_EVENT_MAP = { mouseleave: 'mouseout', mouseenter: 'mouseover' };
var jqLiteMinErr = minErr('jqLite');

/**
 * Converts kebab-case to camelCase.
 * There is also a special case for the ms prefix starting with a lowercase letter.
 * @param name Name to normalize
 */
function cssKebabToCamel(name) {
    return kebabToCamel(name.replace(MS_HACK_REGEXP, 'ms-'));
}

function fnCamelCaseReplace(all, letter) {
  return letter.toUpperCase();
}

/**
 * Converts kebab-case to camelCase.
 * @param name Name to normalize
 */
function kebabToCamel(name) {
  return name
    .replace(DASH_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

var SINGLE_TAG_REGEXP = /^<([\w-]+)\s*\/?>(?:<\/\1>|)$/;
var HTML_REGEXP = /<|&#?\w+;/;
var TAG_NAME_REGEXP = /<([\w:-]+)/;
var XHTML_TAG_REGEXP = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:-]+)[^>]*)\/>/gi;

var wrapMap = {
  'option': [1, '<select multiple="multiple">', '</select>'],

  'thead': [1, '<table>', '</table>'],
  'col': [2, '<table><colgroup>', '</colgroup></table>'],
  'tr': [2, '<table><tbody>', '</tbody></table>'],
  'td': [3, '<table><tbody><tr>', '</tr></tbody></table>'],
  '_default': [0, '', '']
};

wrapMap.optgroup = wrapMap.option;
wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;


function jqLiteIsTextNode(html) {
  return !HTML_REGEXP.test(html);
}

function jqLiteAcceptsData(node) {
  // The window object can accept data but has no nodeType
  // Otherwise we are only interested in elements (1) and documents (9)
  var nodeType = node.nodeType;
  return nodeType === NODE_TYPE_ELEMENT || !nodeType || nodeType === NODE_TYPE_DOCUMENT;
}

function jqLiteHasData(node) {
  for (var key in jqCache[node.ng339]) {
    return true;
  }
  return false;
}

function jqLiteBuildFragment(html, context) {
  var tmp, tag, wrap,
      fragment = context.createDocumentFragment(),
      nodes = [], i;

  if (jqLiteIsTextNode(html)) {
    // Convert non-html into a text node
    nodes.push(context.createTextNode(html));
  } else {
    // Convert html into DOM nodes
    tmp = fragment.appendChild(context.createElement('div'));
    tag = (TAG_NAME_REGEXP.exec(html) || ['', ''])[1].toLowerCase();
    wrap = wrapMap[tag] || wrapMap._default;
    tmp.innerHTML = wrap[1] + html.replace(XHTML_TAG_REGEXP, '<$1></$2>') + wrap[2];

    // Descend through wrappers to the right content
    i = wrap[0];
    while (i--) {
      tmp = tmp.lastChild;
    }

    nodes = concat(nodes, tmp.childNodes);

    tmp = fragment.firstChild;
    tmp.textContent = '';
  }

  // Remove wrapper from fragment
  fragment.textContent = '';
  fragment.innerHTML = ''; // Clear inner HTML
  forEach(nodes, function(node) {
    fragment.appendChild(node);
  });

  return fragment;
}

function jqLiteParseHTML(html, context) {
  context = context || window.document;
  var parsed;

  if ((parsed = SINGLE_TAG_REGEXP.exec(html))) {
    return [context.createElement(parsed[1])];
  }

  if ((parsed = jqLiteBuildFragment(html, context))) {
    return parsed.childNodes;
  }

  return [];
}

function jqLiteWrapNode(node, wrapper) {
  var parent = node.parentNode;

  if (parent) {
    parent.replaceChild(wrapper, node);
  }

  wrapper.appendChild(node);
}


// IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
var jqLiteContains = window.Node.prototype.contains || /** @this */ function(arg) {
  // eslint-disable-next-line no-bitwise
  return !!(this.compareDocumentPosition(arg) & 16);
};

/////////////////////////////////////////////
function JQLite(element) {
  if (element instanceof JQLite) {
    return element;
  }

  var argIsString;

  if (isString(element)) {
    element = trim(element);
    argIsString = true;
  }
  if (!(this instanceof JQLite)) {
    if (argIsString && element.charAt(0) !== '<') {
      throw jqLiteMinErr('nosel', 'Looking up elements via selectors is not supported by jqLite! See: http://docs.angularjs.org/api/angular.element');
    }
    return new JQLite(element);
  }

  if (argIsString) {
    jqLiteAddNodes(this, jqLiteParseHTML(element));
  } else if (isFunction(element)) {
    jqLiteReady(element);
  } else {
    jqLiteAddNodes(this, element);
  }
}

function jqLiteClone(element) {
  return element.cloneNode(true);
}

function jqLiteDealoc(element, onlyDescendants) {
  if (!onlyDescendants && jqLiteAcceptsData(element)) jqLite.cleanData([element]);

  if (element.querySelectorAll) {
    jqLite.cleanData(element.querySelectorAll('*'));
  }
}

function jqLiteOff(element, type, fn, unsupported) {
  if (isDefined(unsupported)) throw jqLiteMinErr('offargs', 'jqLite#off() does not support the `selector` argument');

  var expandoStore = jqLiteExpandoStore(element);
  var events = expandoStore && expandoStore.events;
  var handle = expandoStore && expandoStore.handle;

  if (!handle) return; //no listeners registered

  if (!type) {
    for (type in events) {
      if (type !== '$destroy') {
        element.removeEventListener(type, handle);
      }
      delete events[type];
    }
  } else {

    var removeHandler = function(type) {
      var listenerFns = events[type];
      if (isDefined(fn)) {
        arrayRemove(listenerFns || [], fn);
      }
      if (!(isDefined(fn) && listenerFns && listenerFns.length > 0)) {
        element.removeEventListener(type, handle);
        delete events[type];
      }
    };

    forEach(type.split(' '), function(type) {
      removeHandler(type);
      if (MOUSE_EVENT_MAP[type]) {
        removeHandler(MOUSE_EVENT_MAP[type]);
      }
    });
  }
}

function jqLiteRemoveData(element, name) {
  var expandoId = element.ng339;
  var expandoStore = expandoId && jqCache[expandoId];

  if (expandoStore) {
    if (name) {
      delete expandoStore.data[name];
      return;
    }

    if (expandoStore.handle) {
      if (expandoStore.events.$destroy) {
        expandoStore.handle({}, '$destroy');
      }
      jqLiteOff(element);
    }
    delete jqCache[expandoId];
    element.ng339 = undefined; // don't delete DOM expandos. IE and Chrome don't like it
  }
}


function jqLiteExpandoStore(element, createIfNecessary) {
  var expandoId = element.ng339,
      expandoStore = expandoId && jqCache[expandoId];

  if (createIfNecessary && !expandoStore) {
    element.ng339 = expandoId = jqNextId();
    expandoStore = jqCache[expandoId] = {events: {}, data: {}, handle: undefined};
  }

  return expandoStore;
}


function jqLiteData(element, key, value) {
  if (jqLiteAcceptsData(element)) {
    var prop;

    var isSimpleSetter = isDefined(value);
    var isSimpleGetter = !isSimpleSetter && key && !isObject(key);
    var massGetter = !key;
    var expandoStore = jqLiteExpandoStore(element, !isSimpleGetter);
    var data = expandoStore && expandoStore.data;

    if (isSimpleSetter) { // data('key', value)
      data[kebabToCamel(key)] = value;
    } else {
      if (massGetter) {  // data()
        return data;
      } else {
        if (isSimpleGetter) { // data('key')
          // don't force creation of expandoStore if it doesn't exist yet
          return data && data[kebabToCamel(key)];
        } else { // mass-setter: data({key1: val1, key2: val2})
          for (prop in key) {
            data[kebabToCamel(prop)] = key[prop];
          }
        }
      }
    }
  }
}

function jqLiteHasClass(element, selector) {
  if (!element.getAttribute) return false;
  return ((' ' + (element.getAttribute('class') || '') + ' ').replace(/[\n\t]/g, ' ').
      indexOf(' ' + selector + ' ') > -1);
}

function jqLiteRemoveClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    forEach(cssClasses.split(' '), function(cssClass) {
      element.setAttribute('class', trim(
          (' ' + (element.getAttribute('class') || '') + ' ')
          .replace(/[\n\t]/g, ' ')
          .replace(' ' + trim(cssClass) + ' ', ' '))
      );
    });
  }
}

function jqLiteAddClass(element, cssClasses) {
  if (cssClasses && element.setAttribute) {
    var existingClasses = (' ' + (element.getAttribute('class') || '') + ' ')
                            .replace(/[\n\t]/g, ' ');

    forEach(cssClasses.split(' '), function(cssClass) {
      cssClass = trim(cssClass);
      if (existingClasses.indexOf(' ' + cssClass + ' ') === -1) {
        existingClasses += cssClass + ' ';
      }
    });

    element.setAttribute('class', trim(existingClasses));
  }
}


function jqLiteAddNodes(root, elements) {
  // THIS CODE IS VERY HOT. Don't make changes without benchmarking.

  if (elements) {

    // if a Node (the most common case)
    if (elements.nodeType) {
      root[root.length++] = elements;
    } else {
      var length = elements.length;

      // if an Array or NodeList and not a Window
      if (typeof length === 'number' && elements.window !== elements) {
        if (length) {
          for (var i = 0; i < length; i++) {
            root[root.length++] = elements[i];
          }
        }
      } else {
        root[root.length++] = elements;
      }
    }
  }
}


function jqLiteController(element, name) {
  return jqLiteInheritedData(element, '$' + (name || 'ngController') + 'Controller');
}

function jqLiteInheritedData(element, name, value) {
  // if element is the document object work with the html element instead
  // this makes $(document).scope() possible
  if (element.nodeType === NODE_TYPE_DOCUMENT) {
    element = element.documentElement;
  }
  var names = isArray(name) ? name : [name];

  while (element) {
    for (var i = 0, ii = names.length; i < ii; i++) {
      if (isDefined(value = jqLite.data(element, names[i]))) return value;
    }

    // If dealing with a document fragment node with a host element, and no parent, use the host
    // element as the parent. This enables directives within a Shadow DOM or polyfilled Shadow DOM
    // to lookup parent controllers.
    element = element.parentNode || (element.nodeType === NODE_TYPE_DOCUMENT_FRAGMENT && element.host);
  }
}

function jqLiteEmpty(element) {
  jqLiteDealoc(element, true);
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

function jqLiteRemove(element, keepData) {
  if (!keepData) jqLiteDealoc(element);
  var parent = element.parentNode;
  if (parent) parent.removeChild(element);
}


function jqLiteDocumentLoaded(action, win) {
  win = win || window;
  if (win.document.readyState === 'complete') {
    // Force the action to be run async for consistent behavior
    // from the action's point of view
    // i.e. it will definitely not be in a $apply
    win.setTimeout(action);
  } else {
    // No need to unbind this handler as load is only ever called once
    jqLite(win).on('load', action);
  }
}

function jqLiteReady(fn) {
  function trigger() {
    window.document.removeEventListener('DOMContentLoaded', trigger);
    window.removeEventListener('load', trigger);
    fn();
  }

  // check if document is already loaded
  if (window.document.readyState === 'complete') {
    window.setTimeout(fn);
  } else {
    // We can not use jqLite since we are not done loading and jQuery could be loaded later.

    // Works for modern browsers and IE9
    window.document.addEventListener('DOMContentLoaded', trigger);

    // Fallback to window.onload for others
    window.addEventListener('load', trigger);
  }
}

//////////////////////////////////////////
// Functions which are declared directly.
//////////////////////////////////////////
var JQLitePrototype = JQLite.prototype = {
  ready: jqLiteReady,
  toString: function() {
    var value = [];
    forEach(this, function(e) { value.push('' + e);});
    return '[' + value.join(', ') + ']';
  },

  eq: function(index) {
      return (index >= 0) ? jqLite(this[index]) : jqLite(this[this.length + index]);
  },

  length: 0,
  push: push,
  sort: [].sort,
  splice: [].splice
};

//////////////////////////////////////////
// Functions iterating getter/setters.
// these functions return self on setter and
// value on get.
//////////////////////////////////////////
var BOOLEAN_ATTR = {};
forEach('multiple,selected,checked,disabled,readOnly,required,open'.split(','), function(value) {
  BOOLEAN_ATTR[lowercase(value)] = value;
});
var BOOLEAN_ELEMENTS = {};
forEach('input,select,option,textarea,button,form,details'.split(','), function(value) {
  BOOLEAN_ELEMENTS[value] = true;
});
var ALIASED_ATTR = {
  'ngMinlength': 'minlength',
  'ngMaxlength': 'maxlength',
  'ngMin': 'min',
  'ngMax': 'max',
  'ngPattern': 'pattern',
  'ngStep': 'step'
};

function getBooleanAttrName(element, name) {
  // check dom last since we will most likely fail on name
  var booleanAttr = BOOLEAN_ATTR[name.toLowerCase()];

  // booleanAttr is here twice to minimize DOM access
  return booleanAttr && BOOLEAN_ELEMENTS[nodeName_(element)] && booleanAttr;
}

function getAliasedAttrName(name) {
  return ALIASED_ATTR[name];
}

forEach({
  data: jqLiteData,
  removeData: jqLiteRemoveData,
  hasData: jqLiteHasData,
  cleanData: function jqLiteCleanData(nodes) {
    for (var i = 0, ii = nodes.length; i < ii; i++) {
      jqLiteRemoveData(nodes[i]);
    }
  }
}, function(fn, name) {
  JQLite[name] = fn;
});

forEach({
  data: jqLiteData,
  inheritedData: jqLiteInheritedData,

  scope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$scope') || jqLiteInheritedData(element.parentNode || element, ['$isolateScope', '$scope']);
  },

  isolateScope: function(element) {
    // Can't use jqLiteData here directly so we stay compatible with jQuery!
    return jqLite.data(element, '$isolateScope') || jqLite.data(element, '$isolateScopeNoTemplate');
  },

  controller: jqLiteController,

  injector: function(element) {
    return jqLiteInheritedData(element, '$injector');
  },

  removeAttr: function(element, name) {
    element.removeAttribute(name);
  },

  hasClass: jqLiteHasClass,

  css: function(element, name, value) {
    name = cssKebabToCamel(name);

    if (isDefined(value)) {
      element.style[name] = value;
    } else {
      return element.style[name];
    }
  },

  attr: function(element, name, value) {
    var ret;
    var nodeType = element.nodeType;
    if (nodeType === NODE_TYPE_TEXT || nodeType === NODE_TYPE_ATTRIBUTE || nodeType === NODE_TYPE_COMMENT ||
      !element.getAttribute) {
      return;
    }

    var lowercasedName = lowercase(name);
    var isBooleanAttr = BOOLEAN_ATTR[lowercasedName];

    if (isDefined(value)) {
      // setter

      if (value === null || (value === false && isBooleanAttr)) {
        element.removeAttribute(name);
      } else {
        element.setAttribute(name, isBooleanAttr ? lowercasedName : value);
      }
    } else {
      // getter

      ret = element.getAttribute(name);

      if (isBooleanAttr && ret !== null) {
        ret = lowercasedName;
      }
      // Normalize non-existing attributes to undefined (as jQuery).
      return ret === null ? undefined : ret;
    }
  },

  prop: function(element, name, value) {
    if (isDefined(value)) {
      element[name] = value;
    } else {
      return element[name];
    }
  },

  text: (function() {
    getText.$dv = '';
    return getText;

    function getText(element, value) {
      if (isUndefined(value)) {
        var nodeType = element.nodeType;
        return (nodeType === NODE_TYPE_ELEMENT || nodeType === NODE_TYPE_TEXT) ? element.textContent : '';
      }
      element.textContent = value;
    }
  })(),

  val: function(element, value) {
    if (isUndefined(value)) {
      if (element.multiple && nodeName_(element) === 'select') {
        var result = [];
        forEach(element.options, function(option) {
          if (option.selected) {
            result.push(option.value || option.text);
          }
        });
        return result;
      }
      return element.value;
    }
    element.value = value;
  },

  html: function(element, value) {
    if (isUndefined(value)) {
      return element.innerHTML;
    }
    jqLiteDealoc(element, true);
    element.innerHTML = value;
  },

  empty: jqLiteEmpty
}, function(fn, name) {
  /**
   * Properties: writes return selection, reads return first value
   */
  JQLite.prototype[name] = function(arg1, arg2) {
    var i, key;
    var nodeCount = this.length;

    // jqLiteHasClass has only two arguments, but is a getter-only fn, so we need to special-case it
    // in a way that survives minification.
    // jqLiteEmpty takes no arguments but is a setter.
    if (fn !== jqLiteEmpty &&
        (isUndefined((fn.length === 2 && (fn !== jqLiteHasClass && fn !== jqLiteController)) ? arg1 : arg2))) {
      if (isObject(arg1)) {

        // we are a write, but the object properties are the key/values
        for (i = 0; i < nodeCount; i++) {
          if (fn === jqLiteData) {
            // data() takes the whole object in jQuery
            fn(this[i], arg1);
          } else {
            for (key in arg1) {
              fn(this[i], key, arg1[key]);
            }
          }
        }
        // return self for chaining
        return this;
      } else {
        // we are a read, so read the first child.
        // TODO: do we still need this?
        var value = fn.$dv;
        // Only if we have $dv do we iterate over all, otherwise it is just the first element.
        var jj = (isUndefined(value)) ? Math.min(nodeCount, 1) : nodeCount;
        for (var j = 0; j < jj; j++) {
          var nodeValue = fn(this[j], arg1, arg2);
          value = value ? value + nodeValue : nodeValue;
        }
        return value;
      }
    } else {
      // we are a write, so apply to all children
      for (i = 0; i < nodeCount; i++) {
        fn(this[i], arg1, arg2);
      }
      // return self for chaining
      return this;
    }
  };
});

function createEventHandler(element, events) {
  var eventHandler = function(event, type) {
    // jQuery specific api
    event.isDefaultPrevented = function() {
      return event.defaultPrevented;
    };

    var eventFns = events[type || event.type];
    var eventFnsLength = eventFns ? eventFns.length : 0;

    if (!eventFnsLength) return;

    if (isUndefined(event.immediatePropagationStopped)) {
      var originalStopImmediatePropagation = event.stopImmediatePropagation;
      event.stopImmediatePropagation = function() {
        event.immediatePropagationStopped = true;

        if (event.stopPropagation) {
          event.stopPropagation();
        }

        if (originalStopImmediatePropagation) {
          originalStopImmediatePropagation.call(event);
        }
      };
    }

    event.isImmediatePropagationStopped = function() {
      return event.immediatePropagationStopped === true;
    };

    // Some events have special handlers that wrap the real handler
    var handlerWrapper = eventFns.specialHandlerWrapper || defaultHandlerWrapper;

    // Copy event handlers in case event handlers array is modified during execution.
    if ((eventFnsLength > 1)) {
      eventFns = shallowCopy(eventFns);
    }

    for (var i = 0; i < eventFnsLength; i++) {
      if (!event.isImmediatePropagationStopped()) {
        handlerWrapper(element, event, eventFns[i]);
      }
    }
  };

  // TODO: this is a hack for angularMocks/clearDataCache that makes it possible to deregister all
  //       events on `element`
  eventHandler.elem = element;
  return eventHandler;
}

function defaultHandlerWrapper(element, event, handler) {
  handler.call(element, event);
}

function specialMouseHandlerWrapper(target, event, handler) {
  // Refer to jQuery's implementation of mouseenter & mouseleave
  // Read about mouseenter and mouseleave:
  // http://www.quirksmode.org/js/events_mouse.html#link8
  var related = event.relatedTarget;
  // For mousenter/leave call the handler if related is outside the target.
  // NB: No relatedTarget if the mouse left/entered the browser window
  if (!related || (related !== target && !jqLiteContains.call(target, related))) {
    handler.call(target, event);
  }
}

//////////////////////////////////////////
// Functions iterating traversal.
// These functions chain results into a single
// selector.
//////////////////////////////////////////
forEach({
  removeData: jqLiteRemoveData,

  on: function jqLiteOn(element, type, fn, unsupported) {
    if (isDefined(unsupported)) throw jqLiteMinErr('onargs', 'jqLite#on() does not support the `selector` or `eventData` parameters');

    // Do not add event handlers to non-elements because they will not be cleaned up.
    if (!jqLiteAcceptsData(element)) {
      return;
    }

    var expandoStore = jqLiteExpandoStore(element, true);
    var events = expandoStore.events;
    var handle = expandoStore.handle;

    if (!handle) {
      handle = expandoStore.handle = createEventHandler(element, events);
    }

    // http://jsperf.com/string-indexof-vs-split
    var types = type.indexOf(' ') >= 0 ? type.split(' ') : [type];
    var i = types.length;

    var addHandler = function(type, specialHandlerWrapper, noEventListener) {
      var eventFns = events[type];

      if (!eventFns) {
        eventFns = events[type] = [];
        eventFns.specialHandlerWrapper = specialHandlerWrapper;
        if (type !== '$destroy' && !noEventListener) {
          element.addEventListener(type, handle);
        }
      }

      eventFns.push(fn);
    };

    while (i--) {
      type = types[i];
      if (MOUSE_EVENT_MAP[type]) {
        addHandler(MOUSE_EVENT_MAP[type], specialMouseHandlerWrapper);
        addHandler(type, undefined, true);
      } else {
        addHandler(type);
      }
    }
  },

  off: jqLiteOff,

  one: function(element, type, fn) {
    element = jqLite(element);

    //add the listener twice so that when it is called
    //you can remove the original function and still be
    //able to call element.off(ev, fn) normally
    element.on(type, function onFn() {
      element.off(type, fn);
      element.off(type, onFn);
    });
    element.on(type, fn);
  },

  replaceWith: function(element, replaceNode) {
    var index, parent = element.parentNode;
    jqLiteDealoc(element);
    forEach(new JQLite(replaceNode), function(node) {
      if (index) {
        parent.insertBefore(node, index.nextSibling);
      } else {
        parent.replaceChild(node, element);
      }
      index = node;
    });
  },

  children: function(element) {
    var children = [];
    forEach(element.childNodes, function(element) {
      if (element.nodeType === NODE_TYPE_ELEMENT) {
        children.push(element);
      }
    });
    return children;
  },

  contents: function(element) {
    return element.contentDocument || element.childNodes || [];
  },

  append: function(element, node) {
    var nodeType = element.nodeType;
    if (nodeType !== NODE_TYPE_ELEMENT && nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT) return;

    node = new JQLite(node);

    for (var i = 0, ii = node.length; i < ii; i++) {
      var child = node[i];
      element.appendChild(child);
    }
  },

  prepend: function(element, node) {
    if (element.nodeType === NODE_TYPE_ELEMENT) {
      var index = element.firstChild;
      forEach(new JQLite(node), function(child) {
        element.insertBefore(child, index);
      });
    }
  },

  wrap: function(element, wrapNode) {
    jqLiteWrapNode(element, jqLite(wrapNode).eq(0).clone()[0]);
  },

  remove: jqLiteRemove,

  detach: function(element) {
    jqLiteRemove(element, true);
  },

  after: function(element, newElement) {
    var index = element, parent = element.parentNode;

    if (parent) {
      newElement = new JQLite(newElement);

      for (var i = 0, ii = newElement.length; i < ii; i++) {
        var node = newElement[i];
        parent.insertBefore(node, index.nextSibling);
        index = node;
      }
    }
  },

  addClass: jqLiteAddClass,
  removeClass: jqLiteRemoveClass,

  toggleClass: function(element, selector, condition) {
    if (selector) {
      forEach(selector.split(' '), function(className) {
        var classCondition = condition;
        if (isUndefined(classCondition)) {
          classCondition = !jqLiteHasClass(element, className);
        }
        (classCondition ? jqLiteAddClass : jqLiteRemoveClass)(element, className);
      });
    }
  },

  parent: function(element) {
    var parent = element.parentNode;
    return parent && parent.nodeType !== NODE_TYPE_DOCUMENT_FRAGMENT ? parent : null;
  },

  next: function(element) {
    return element.nextElementSibling;
  },

  find: function(element, selector) {
    if (element.getElementsByTagName) {
      return element.getElementsByTagName(selector);
    } else {
      return [];
    }
  },

  clone: jqLiteClone,

  triggerHandler: function(element, event, extraParameters) {

    var dummyEvent, eventFnsCopy, handlerArgs;
    var eventName = event.type || event;
    var expandoStore = jqLiteExpandoStore(element);
    var events = expandoStore && expandoStore.events;
    var eventFns = events && events[eventName];

    if (eventFns) {
      // Create a dummy event to pass to the handlers
      dummyEvent = {
        preventDefault: function() { this.defaultPrevented = true; },
        isDefaultPrevented: function() { return this.defaultPrevented === true; },
        stopImmediatePropagation: function() { this.immediatePropagationStopped = true; },
        isImmediatePropagationStopped: function() { return this.immediatePropagationStopped === true; },
        stopPropagation: noop,
        type: eventName,
        target: element
      };

      // If a custom event was provided then extend our dummy event with it
      if (event.type) {
        dummyEvent = extend(dummyEvent, event);
      }

      // Copy event handlers in case event handlers array is modified during execution.
      eventFnsCopy = shallowCopy(eventFns);
      handlerArgs = extraParameters ? [dummyEvent].concat(extraParameters) : [dummyEvent];

      forEach(eventFnsCopy, function(fn) {
        if (!dummyEvent.isImmediatePropagationStopped()) {
          fn.apply(element, handlerArgs);
        }
      });
    }
  }
}, function(fn, name) {
  /**
   * chaining functions
   */
  JQLite.prototype[name] = function(arg1, arg2, arg3) {
    var value;

    for (var i = 0, ii = this.length; i < ii; i++) {
      if (isUndefined(value)) {
        value = fn(this[i], arg1, arg2, arg3);
        if (isDefined(value)) {
          // any function which returns a value needs to be wrapped
          value = jqLite(value);
        }
      } else {
        jqLiteAddNodes(value, fn(this[i], arg1, arg2, arg3));
      }
    }
    return isDefined(value) ? value : this;
  };
});

// bind legacy bind/unbind to on/off
JQLite.prototype.bind = JQLite.prototype.on;
JQLite.prototype.unbind = JQLite.prototype.off;


// Provider for private $$jqLite service
/** @this */
function $$jqLiteProvider() {
  this.$get = function $$jqLite() {
    return extend(JQLite, {
      hasClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteHasClass(node, classes);
      },
      addClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteAddClass(node, classes);
      },
      removeClass: function(node, classes) {
        if (node.attr) node = node[0];
        return jqLiteRemoveClass(node, classes);
      }
    });
  };
}

/**
 * Computes a hash of an 'obj'.
 * Hash of a:
 *  string is string
 *  number is number as string
 *  object is either result of calling $$hashKey function on the object or uniquely generated id,
 *         that is also assigned to the $$hashKey property of the object.
 *
 * @param obj
 * @returns {string} hash string such that the same input will have the same hash string.
 *         The resulting string key is in 'type:hashKey' format.
 */
function hashKey(obj, nextUidFn) {
  var key = obj && obj.$$hashKey;

  if (key) {
    if (typeof key === 'function') {
      key = obj.$$hashKey();
    }
    return key;
  }

  var objType = typeof obj;
  if (objType === 'function' || (objType === 'object' && obj !== null)) {
    key = obj.$$hashKey = objType + ':' + (nextUidFn || nextUid)();
  } else {
    key = objType + ':' + obj;
  }

  return key;
}

// A minimal ES2015 Map implementation.
// Should be bug/feature equivalent to the native implementations of supported browsers
// (for the features required in Angular).
// See https://kangax.github.io/compat-table/es6/#test-Map
var nanKey = Object.create(null);
function NgMapShim() {
  this._keys = [];
  this._values = [];
  this._lastKey = NaN;
  this._lastIndex = -1;
}
NgMapShim.prototype = {
  _idx: function(key) {
    if (key === this._lastKey) {
      return this._lastIndex;
    }
    this._lastKey = key;
    this._lastIndex = this._keys.indexOf(key);
    return this._lastIndex;
  },
  _transformKey: function(key) {
    return isNumberNaN(key) ? nanKey : key;
  },
  get: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx !== -1) {
      return this._values[idx];
    }
  },
  set: function(key, value) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      idx = this._lastIndex = this._keys.length;
    }
    this._keys[idx] = key;
    this._values[idx] = value;

    // Support: IE11
    // Do not `return this` to simulate the partial IE11 implementation
  },
  delete: function(key) {
    key = this._transformKey(key);
    var idx = this._idx(key);
    if (idx === -1) {
      return false;
    }
    this._keys.splice(idx, 1);
    this._values.splice(idx, 1);
    this._lastKey = NaN;
    this._lastIndex = -1;
    return true;
  }
};

// For now, always use `NgMapShim`, even if `window.Map` is available. Some native implementations
// are still buggy (often in subtle ways) and can cause hard-to-debug failures. When native `Map`
// implementations get more stable, we can reconsider switching to `window.Map` (when available).
var NgMap = NgMapShim;

var $$MapProvider = [/** @this */function() {
  this.$get = [function() {
    return NgMap;
  }];
}];

/**
 * @ngdoc function
 * @module ng
 * @name angular.injector
 * @kind function
 *
 * @description
 * Creates an injector object that can be used for retrieving services as well as for
 * dependency injection (see {@link guide/di dependency injection}).
 *
 * @param {Array.<string|Function>} modules A list of module functions or their aliases. See
 *     {@link angular.module}. The `ng` module must be explicitly added.
 * @param {boolean=} [strictDi=false] Whether the injector should be in strict mode, which
 *     disallows argument name annotation inference.
 * @returns {injector} Injector object. See {@link auto.$injector $injector}.
 *
 * @example
 * Typical usage
 * ```js
 *   // create an injector
 *   var $injector = angular.injector(['ng']);
 *
 *   // use the injector to kick off your application
 *   // use the type inference to auto inject arguments, or use implicit injection
 *   $injector.invoke(function($rootScope, $compile, $document) {
 *     $compile($document)($rootScope);
 *     $rootScope.$digest();
 *   });
 * ```
 *
 * Sometimes you want to get access to the injector of a currently running Angular app
 * from outside Angular. Perhaps, you want to inject and compile some markup after the
 * application has been bootstrapped. You can do this using the extra `injector()` added
 * to JQuery/jqLite elements. See {@link angular.element}.
 *
 * *This is fairly rare but could be the case if a third party library is injecting the
 * markup.*
 *
 * In the following example a new block of HTML containing a `ng-controller`
 * directive is added to the end of the document body by JQuery. We then compile and link
 * it into the current AngularJS scope.
 *
 * ```js
 * var $div = $('<div ng-controller="MyCtrl">{{content.label}}</div>');
 * $(document.body).append($div);
 *
 * angular.element(document).injector().invoke(function($compile) {
 *   var scope = angular.element($div).scope();
 *   $compile($div)(scope);
 * });
 * ```
 */


/**
 * @ngdoc module
 * @name auto
 * @installation
 * @description
 *
 * Implicit module which gets automatically added to each {@link auto.$injector $injector}.
 */

var ARROW_ARG = /^([^(]+?)=>/;
var FN_ARGS = /^[^(]*\(\s*([^)]*)\)/m;
var FN_ARG_SPLIT = /,/;
var FN_ARG = /^\s*(_?)(\S+?)\1\s*$/;
var STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
var $injectorMinErr = minErr('$injector');

function stringifyFn(fn) {
  return Function.prototype.toString.call(fn);
}

function extractArgs(fn) {
  var fnText = stringifyFn(fn).replace(STRIP_COMMENTS, ''),
      args = fnText.match(ARROW_ARG) || fnText.match(FN_ARGS);
  return args;
}

function anonFn(fn) {
  // For anonymous functions, showing at the very least the function signature can help in
  // debugging.
  var args = extractArgs(fn);
  if (args) {
    return 'function(' + (args[1] || '').replace(/[\s\r\n]+/, ' ') + ')';
  }
  return 'fn';
}

function annotate(fn, strictDi, name) {
  var $inject,
      argDecl,
      last;

  if (typeof fn === 'function') {
    if (!($inject = fn.$inject)) {
      $inject = [];
      if (fn.length) {
        if (strictDi) {
          if (!isString(name) || !name) {
            name = fn.name || anonFn(fn);
          }
          throw $injectorMinErr('strictdi',
            '{0} is not using explicit annotation and cannot be invoked in strict mode', name);
        }
        argDecl = extractArgs(fn);
        forEach(argDecl[1].split(FN_ARG_SPLIT), function(arg) {
          arg.replace(FN_ARG, function(all, underscore, name) {
            $inject.push(name);
          });
        });
      }
      fn.$inject = $inject;
    }
  } else if (isArray(fn)) {
    last = fn.length - 1;
    assertArgFn(fn[last], 'fn');
    $inject = fn.slice(0, last);
  } else {
    assertArgFn(fn, 'fn', true);
  }
  return $inject;
}

///////////////////////////////////////

/**
 * @ngdoc service
 * @name $injector
 *
 * @description
 *
 * `$injector` is used to retrieve object instances as defined by
 * {@link auto.$provide provider}, instantiate types, invoke methods,
 * and load modules.
 *
 * The following always holds true:
 *
 * ```js
 *   var $injector = angular.injector();
 *   expect($injector.get('$injector')).toBe($injector);
 *   expect($injector.invoke(function($injector) {
 *     return $injector;
 *   })).toBe($injector);
 * ```
 *
 * # Injection Function Annotation
 *
 * JavaScript does not have annotations, and annotations are needed for dependency injection. The
 * following are all valid ways of annotating function with injection arguments and are equivalent.
 *
 * ```js
 *   // inferred (only works if code not minified/obfuscated)
 *   $injector.invoke(function(serviceA){});
 *
 *   // annotated
 *   function explicit(serviceA) {};
 *   explicit.$inject = ['serviceA'];
 *   $injector.invoke(explicit);
 *
 *   // inline
 *   $injector.invoke(['serviceA', function(serviceA){}]);
 * ```
 *
 * ## Inference
 *
 * In JavaScript calling `toString()` on a function returns the function definition. The definition
 * can then be parsed and the function arguments can be extracted. This method of discovering
 * annotations is disallowed when the injector is in strict mode.
 * *NOTE:* This does not work with minification, and obfuscation tools since these tools change the
 * argument names.
 *
 * ## `$inject` Annotation
 * By adding an `$inject` property onto a function the injection parameters can be specified.
 *
 * ## Inline
 * As an array of injection names, where the last item in the array is the function to call.
 */

/**
 * @ngdoc property
 * @name $injector#modules
 * @type {Object}
 * @description
 * A hash containing all the modules that have been loaded into the
 * $injector.
 *
 * You can use this property to find out information about a module via the
 * {@link angular.Module#info `myModule.info(...)`} method.
 *
 * For example:
 *
 * ```
 * var info = $injector.modules['ngAnimate'].info();
 * ```
 *
 * **Do not use this property to attempt to modify the modules after the application
 * has been bootstrapped.**
 */


/**
 * @ngdoc method
 * @name $injector#get
 *
 * @description
 * Return an instance of the service.
 *
 * @param {string} name The name of the instance to retrieve.
 * @param {string=} caller An optional string to provide the origin of the function call for error messages.
 * @return {*} The instance.
 */

/**
 * @ngdoc method
 * @name $injector#invoke
 *
 * @description
 * Invoke the method and supply the method arguments from the `$injector`.
 *
 * @param {Function|Array.<string|Function>} fn The injectable function to invoke. Function parameters are
 *   injected according to the {@link guide/di $inject Annotation} rules.
 * @param {Object=} self The `this` for the invoked method.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 *                         object first, before the `$injector` is consulted.
 * @returns {*} the value returned by the invoked `fn` function.
 */

/**
 * @ngdoc method
 * @name $injector#has
 *
 * @description
 * Allows the user to query if the particular service exists.
 *
 * @param {string} name Name of the service to query.
 * @returns {boolean} `true` if injector has given service.
 */

/**
 * @ngdoc method
 * @name $injector#instantiate
 * @description
 * Create a new instance of JS type. The method takes a constructor function, invokes the new
 * operator, and supplies all of the arguments to the constructor function as specified by the
 * constructor annotation.
 *
 * @param {Function} Type Annotated constructor function.
 * @param {Object=} locals Optional object. If preset then any argument names are read from this
 * object first, before the `$injector` is consulted.
 * @returns {Object} new instance of `Type`.
 */

/**
 * @ngdoc method
 * @name $injector#annotate
 *
 * @description
 * Returns an array of service names which the function is requesting for injection. This API is
 * used by the injector to determine which services need to be injected into the function when the
 * function is invoked. There are three ways in which the function can be annotated with the needed
 * dependencies.
 *
 * # Argument names
 *
 * The simplest form is to extract the dependencies from the arguments of the function. This is done
 * by converting the function into a string using `toString()` method and extracting the argument
 * names.
 * ```js
 *   // Given
 *   function MyController($scope, $route) {
 *     // ...
 *   }
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * You can disallow this method by using strict injection mode.
 *
 * This method does not work with code minification / obfuscation. For this reason the following
 * annotation strategies are supported.
 *
 * # The `$inject` property
 *
 * If a function has an `$inject` property and its value is an array of strings, then the strings
 * represent names of services to be injected into the function.
 * ```js
 *   // Given
 *   var MyController = function(obfuscatedScope, obfuscatedRoute) {
 *     // ...
 *   }
 *   // Define function dependencies
 *   MyController['$inject'] = ['$scope', '$route'];
 *
 *   // Then
 *   expect(injector.annotate(MyController)).toEqual(['$scope', '$route']);
 * ```
 *
 * # The array notation
 *
 * It is often desirable to inline Injected functions and that's when setting the `$inject` property
 * is very inconvenient. In these situations using the array notation to specify the dependencies in
 * a way that survives minification is a better choice:
 *
 * ```js
 *   // We wish to write this (not minification / obfuscation safe)
 *   injector.invoke(function($compile, $rootScope) {
 *     // ...
 *   });
 *
 *   // We are forced to write break inlining
 *   var tmpFn = function(obfuscatedCompile, obfuscatedRootScope) {
 *     // ...
 *   };
 *   tmpFn.$inject = ['$compile', '$rootScope'];
 *   injector.invoke(tmpFn);
 *
 *   // To better support inline function the inline annotation is supported
 *   injector.invoke(['$compile', '$rootScope', function(obfCompile, obfRootScope) {
 *     // ...
 *   }]);
 *
 *   // Therefore
 *   expect(injector.annotate(
 *      ['$compile', '$rootScope', function(obfus_$compile, obfus_$rootScope) {}])
 *    ).toEqual(['$compile', '$rootScope']);
 * ```
 *
 * @param {Function|Array.<string|Function>} fn Function for which dependent service names need to
 * be retrieved as described above.
 *
 * @param {boolean=} [strictDi=false] Disallow argument name annotation inference.
 *
 * @returns {Array.<string>} The names of the services which the function requires.
 */



/**
 * @ngdoc service
 * @name $provide
 *
 * @description
 *
 * The {@link auto.$provide $provide} service has a number of methods for registering components
 * with the {@link auto.$injector $injector}. Many of these functions are also exposed on
 * {@link angular.Module}.
 *
 * An Angular **service** is a singleton object created by a **service factory**.  These **service
 * factories** are functions which, in turn, are created by a **service provider**.
 * The **service providers** are constructor functions. When instantiated they must contain a
 * property called `$get`, which holds the **service factory** function.
 *
 * When you request a service, the {@link auto.$injector $injector} is responsible for finding the
 * correct **service provider**, instantiating it and then calling its `$get` **service factory**
 * function to get the instance of the **service**.
 *
 * Often services have no configuration options and there is no need to add methods to the service
 * provider.  The provider will be no more than a constructor function with a `$get` property. For
 * these cases the {@link auto.$provide $provide} service has additional helper methods to register
 * services without specifying a provider.
 *
 * * {@link auto.$provide#provider provider(name, provider)} - registers a **service provider** with the
 *     {@link auto.$injector $injector}
 * * {@link auto.$provide#constant constant(name, obj)} - registers a value/object that can be accessed by
 *     providers and services.
 * * {@link auto.$provide#value value(name, obj)} - registers a value/object that can only be accessed by
 *     services, not providers.
 * * {@link auto.$provide#factory factory(name, fn)} - registers a service **factory function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will contain the
 *     given factory function.
 * * {@link auto.$provide#service service(name, Fn)} - registers a **constructor function**
 *     that will be wrapped in a **service provider** object, whose `$get` property will instantiate
 *      a new object using the given constructor function.
 * * {@link auto.$provide#decorator decorator(name, decorFn)} - registers a **decorator function** that
 *      will be able to modify or replace the implementation of another service.
 *
 * See the individual methods for more information and examples.
 */

/**
 * @ngdoc method
 * @name $provide#provider
 * @description
 *
 * Register a **provider function** with the {@link auto.$injector $injector}. Provider functions
 * are constructor functions, whose instances are responsible for "providing" a factory for a
 * service.
 *
 * Service provider names start with the name of the service they provide followed by `Provider`.
 * For example, the {@link ng.$log $log} service has a provider called
 * {@link ng.$logProvider $logProvider}.
 *
 * Service provider objects can have additional methods which allow configuration of the provider
 * and its service. Importantly, you can configure what kind of service is created by the `$get`
 * method, or how that service will act. For example, the {@link ng.$logProvider $logProvider} has a
 * method {@link ng.$logProvider#debugEnabled debugEnabled}
 * which lets you specify whether the {@link ng.$log $log} service will log debug messages to the
 * console or not.
 *
 * @param {string} name The name of the instance. NOTE: the provider will be available under `name +
                        'Provider'` key.
 * @param {(Object|function())} provider If the provider is:
 *
 *   - `Object`: then it should have a `$get` method. The `$get` method will be invoked using
 *     {@link auto.$injector#invoke $injector.invoke()} when an instance needs to be created.
 *   - `Constructor`: a new instance of the provider will be created using
 *     {@link auto.$injector#instantiate $injector.instantiate()}, then treated as `object`.
 *
 * @returns {Object} registered provider instance

 * @example
 *
 * The following example shows how to create a simple event tracking service and register it using
 * {@link auto.$provide#provider $provide.provider()}.
 *
 * ```js
 *  // Define the eventTracker provider
 *  function EventTrackerProvider() {
 *    var trackingUrl = '/track';
 *
 *    // A provider method for configuring where the tracked events should been saved
 *    this.setTrackingUrl = function(url) {
 *      trackingUrl = url;
 *    };
 *
 *    // The service factory function
 *    this.$get = ['$http', function($http) {
 *      var trackedEvents = {};
 *      return {
 *        // Call this to track an event
 *        event: function(event) {
 *          var count = trackedEvents[event] || 0;
 *          count += 1;
 *          trackedEvents[event] = count;
 *          return count;
 *        },
 *        // Call this to save the tracked events to the trackingUrl
 *        save: function() {
 *          $http.post(trackingUrl, trackedEvents);
 *        }
 *      };
 *    }];
 *  }
 *
 *  describe('eventTracker', function() {
 *    var postSpy;
 *
 *    beforeEach(module(function($provide) {
 *      // Register the eventTracker provider
 *      $provide.provider('eventTracker', EventTrackerProvider);
 *    }));
 *
 *    beforeEach(module(function(eventTrackerProvider) {
 *      // Configure eventTracker provider
 *      eventTrackerProvider.setTrackingUrl('/custom-track');
 *    }));
 *
 *    it('tracks events', inject(function(eventTracker) {
 *      expect(eventTracker.event('login')).toEqual(1);
 *      expect(eventTracker.event('login')).toEqual(2);
 *    }));
 *
 *    it('saves to the tracking url', inject(function(eventTracker, $http) {
 *      postSpy = spyOn($http, 'post');
 *      eventTracker.event('login');
 *      eventTracker.save();
 *      expect(postSpy).toHaveBeenCalled();
 *      expect(postSpy.mostRecentCall.args[0]).not.toEqual('/track');
 *      expect(postSpy.mostRecentCall.args[0]).toEqual('/custom-track');
 *      expect(postSpy.mostRecentCall.args[1]).toEqual({ 'login': 1 });
 *    }));
 *  });
 * ```
 */

/**
 * @ngdoc method
 * @name $provide#factory
 * @description
 *
 * Register a **service factory**, which will be called to return the service instance.
 * This is short for registering a service where its provider consists of only a `$get` property,
 * which is the given service factory function.
 * You should use {@link auto.$provide#factory $provide.factory(getFn)} if you do not need to
 * configure your service in a provider.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} $getFn The injectable $getFn for the instance creation.
 *                      Internally this is a short hand for `$provide.provider(name, {$get: $getFn})`.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service
 * ```js
 *   $provide.factory('ping', ['$http', function($http) {
 *     return function ping() {
 *       return $http.send('/ping');
 *     };
 *   }]);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#service
 * @description
 *
 * Register a **service constructor**, which will be invoked with `new` to create the service
 * instance.
 * This is short for registering a service where its provider's `$get` property is a factory
 * function that returns an instance instantiated by the injector from the service constructor
 * function.
 *
 * Internally it looks a bit like this:
 *
 * ```
 * {
 *   $get: function() {
 *     return $injector.instantiate(constructor);
 *   }
 * }
 * ```
 *
 *
 * You should use {@link auto.$provide#service $provide.service(class)} if you define your service
 * as a type/class.
 *
 * @param {string} name The name of the instance.
 * @param {Function|Array.<string|Function>} constructor An injectable class (constructor function)
 *     that will be instantiated.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here is an example of registering a service using
 * {@link auto.$provide#service $provide.service(class)}.
 * ```js
 *   var Ping = function($http) {
 *     this.$http = $http;
 *   };
 *
 *   Ping.$inject = ['$http'];
 *
 *   Ping.prototype.send = function() {
 *     return this.$http.get('/ping');
 *   };
 *   $provide.service('ping', Ping);
 * ```
 * You would then inject and use this service like this:
 * ```js
 *   someModule.controller('Ctrl', ['ping', function(ping) {
 *     ping.send();
 *   }]);
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#value
 * @description
 *
 * Register a **value service** with the {@link auto.$injector $injector}, such as a string, a
 * number, an array, an object or a function. This is short for registering a service where its
 * provider's `$get` property is a factory function that takes no arguments and returns the **value
 * service**. That also means it is not possible to inject other services into a value service.
 *
 * Value services are similar to constant services, except that they cannot be injected into a
 * module configuration function (see {@link angular.Module#config}) but they can be overridden by
 * an Angular {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the instance.
 * @param {*} value The value.
 * @returns {Object} registered provider instance
 *
 * @example
 * Here are some examples of creating value services.
 * ```js
 *   $provide.value('ADMIN_USER', 'admin');
 *
 *   $provide.value('RoleLookup', { admin: 0, writer: 1, reader: 2 });
 *
 *   $provide.value('halfOf', function(value) {
 *     return value / 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#constant
 * @description
 *
 * Register a **constant service** with the {@link auto.$injector $injector}, such as a string,
 * a number, an array, an object or a function. Like the {@link auto.$provide#value value}, it is not
 * possible to inject other services into a constant.
 *
 * But unlike {@link auto.$provide#value value}, a constant can be
 * injected into a module configuration function (see {@link angular.Module#config}) and it cannot
 * be overridden by an Angular {@link auto.$provide#decorator decorator}.
 *
 * @param {string} name The name of the constant.
 * @param {*} value The constant value.
 * @returns {Object} registered instance
 *
 * @example
 * Here a some examples of creating constants:
 * ```js
 *   $provide.constant('SHARD_HEIGHT', 306);
 *
 *   $provide.constant('MY_COLOURS', ['red', 'blue', 'grey']);
 *
 *   $provide.constant('double', function(value) {
 *     return value * 2;
 *   });
 * ```
 */


/**
 * @ngdoc method
 * @name $provide#decorator
 * @description
 *
 * Register a **decorator function** with the {@link auto.$injector $injector}. A decorator function
 * intercepts the creation of a service, allowing it to override or modify the behavior of the
 * service. The return value of the decorator function may be the original service, or a new service
 * that replaces (or wraps and delegates to) the original service.
 *
 * You can find out more about using decorators in the {@link guide/decorators} guide.
 *
 * @param {string} name The name of the service to decorate.
 * @param {Function|Array.<string|Function>} decorator This function will be invoked when the service needs to be
 *    provided and should return the decorated service instance. The function is called using
 *    the {@link auto.$injector#invoke injector.invoke} method and is therefore fully injectable.
 *    Local injection arguments:
 *
 *    * `$delegate` - The original service instance, which can be replaced, monkey patched, configured,
 *      decorated or delegated to.
 *
 * @example
 * Here we decorate the {@link ng.$log $log} service to convert warnings to errors by intercepting
 * calls to {@link ng.$log#error $log.warn()}.
 * ```js
 *   $provide.decorator('$log', ['$delegate', function($delegate) {
 *     $delegate.warn = $delegate.error;
 *     return $delegate;
 *   }]);
 * ```
 */


function createInjector(modulesToLoad, strictDi) {
  strictDi = (strictDi === true);
  var INSTANTIATING = {},
      providerSuffix = 'Provider',
      path = [],
      loadedModules = new NgMap(),
      providerCache = {
        $provide: {
            provider: supportObject(provider),
            factory: supportObject(factory),
            service: supportObject(service),
            value: supportObject(value),
            constant: supportObject(constant),
            decorator: decorator
          }
      },
      providerInjector = (providerCache.$injector =
          createInternalInjector(providerCache, function(serviceName, caller) {
            if (angular.isString(caller)) {
              path.push(caller);
            }
            throw $injectorMinErr('unpr', 'Unknown provider: {0}', path.join(' <- '));
          })),
      instanceCache = {},
      protoInstanceInjector =
          createInternalInjector(instanceCache, function(serviceName, caller) {
            var provider = providerInjector.get(serviceName + providerSuffix, caller);
            return instanceInjector.invoke(
                provider.$get, provider, undefined, serviceName);
          }),
      instanceInjector = protoInstanceInjector;

  providerCache['$injector' + providerSuffix] = { $get: valueFn(protoInstanceInjector) };
  instanceInjector.modules = providerInjector.modules = createMap();
  var runBlocks = loadModules(modulesToLoad);
  instanceInjector = protoInstanceInjector.get('$injector');
  instanceInjector.strictDi = strictDi;
  forEach(runBlocks, function(fn) { if (fn) instanceInjector.invoke(fn); });

  return instanceInjector;

  ////////////////////////////////////
  // $provider
  ////////////////////////////////////

  function supportObject(delegate) {
    return function(key, value) {
      if (isObject(key)) {
        forEach(key, reverseParams(delegate));
      } else {
        return delegate(key, value);
      }
    };
  }

  function provider(name, provider_) {
    assertNotHasOwnProperty(name, 'service');
    if (isFunction(provider_) || isArray(provider_)) {
      provider_ = providerInjector.instantiate(provider_);
    }
    if (!provider_.$get) {
      throw $injectorMinErr('pget', 'Provider \'{0}\' must define $get factory method.', name);
    }
    return (providerCache[name + providerSuffix] = provider_);
  }

  function enforceReturnValue(name, factory) {
    return /** @this */ function enforcedReturnValue() {
      var result = instanceInjector.invoke(factory, this);
      if (isUndefined(result)) {
        throw $injectorMinErr('undef', 'Provider \'{0}\' must return a value from $get factory method.', name);
      }
      return result;
    };
  }

  function factory(name, factoryFn, enforce) {
    return provider(name, {
      $get: enforce !== false ? enforceReturnValue(name, factoryFn) : factoryFn
    });
  }

  function service(name, constructor) {
    return factory(name, ['$injector', function($injector) {
      return $injector.instantiate(constructor);
    }]);
  }

  function value(name, val) { return factory(name, valueFn(val), false); }

  function constant(name, value) {
    assertNotHasOwnProperty(name, 'constant');
    providerCache[name] = value;
    instanceCache[name] = value;
  }

  function decorator(serviceName, decorFn) {
    var origProvider = providerInjector.get(serviceName + providerSuffix),
        orig$get = origProvider.$get;

    origProvider.$get = function() {
      var origInstance = instanceInjector.invoke(orig$get, origProvider);
      return instanceInjector.invoke(decorFn, null, {$delegate: origInstance});
    };
  }

  ////////////////////////////////////
  // Module Loading
  ////////////////////////////////////
  function loadModules(modulesToLoad) {
    assertArg(isUndefined(modulesToLoad) || isArray(modulesToLoad), 'modulesToLoad', 'not an array');
    var runBlocks = [], moduleFn;
    forEach(modulesToLoad, function(module) {
      if (loadedModules.get(module)) return;
      loadedModules.set(module, true);

      function runInvokeQueue(queue) {
        var i, ii;
        for (i = 0, ii = queue.length; i < ii; i++) {
          var invokeArgs = queue[i],
              provider = providerInjector.get(invokeArgs[0]);

          provider[invokeArgs[1]].apply(provider, invokeArgs[2]);
        }
      }

      try {
        if (isString(module)) {
          moduleFn = angularModule(module);
          instanceInjector.modules[module] = moduleFn;
          runBlocks = runBlocks.concat(loadModules(moduleFn.requires)).concat(moduleFn._runBlocks);
          runInvokeQueue(moduleFn._invokeQueue);
          runInvokeQueue(moduleFn._configBlocks);
        } else if (isFunction(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else if (isArray(module)) {
            runBlocks.push(providerInjector.invoke(module));
        } else {
          assertArgFn(module, 'module');
        }
      } catch (e) {
        if (isArray(module)) {
          module = module[module.length - 1];
        }
        if (e.message && e.stack && e.stack.indexOf(e.message) === -1) {
          // Safari & FF's stack traces don't contain error.message content
          // unlike those of Chrome and IE
          // So if stack doesn't contain message, we create a new string that contains both.
          // Since error.stack is read-only in Safari, I'm overriding e and not e.stack here.
          // eslint-disable-next-line no-ex-assign
          e = e.message + '\n' + e.stack;
        }
        throw $injectorMinErr('modulerr', 'Failed to instantiate module {0} due to:\n{1}',
                  module, e.stack || e.message || e);
      }
    });
    return runBlocks;
  }

  ////////////////////////////////////
  // internal Injector
  ////////////////////////////////////

  function createInternalInjector(cache, factory) {

    function getService(serviceName, caller) {
      if (cache.hasOwnProperty(serviceName)) {
        if (cache[serviceName] === INSTANTIATING) {
          throw $injectorMinErr('cdep', 'Circular dependency found: {0}',
                    serviceName + ' <- ' + path.join(' <- '));
        }
        return cache[serviceName];
      } else {
        try {
          path.unshift(serviceName);
          cache[serviceName] = INSTANTIATING;
          cache[serviceName] = factory(serviceName, caller);
          return cache[serviceName];
        } catch (err) {
          if (cache[serviceName] === INSTANTIATING) {
            delete cache[serviceName];
          }
          throw err;
        } finally {
          path.shift();
        }
      }
    }


    function injectionArgs(fn, locals, serviceName) {
      var args = [],
          $inject = createInjector.$$annotate(fn, strictDi, serviceName);

      for (var i = 0, length = $inject.length; i < length; i++) {
        var key = $inject[i];
        if (typeof key !== 'string') {
          throw $injectorMinErr('itkn',
                  'Incorrect injection token! Expected service name as string, got {0}', key);
        }
        args.push(locals && locals.hasOwnProperty(key) ? locals[key] :
                                                         getService(key, serviceName));
      }
      return args;
    }

    function isClass(func) {
      // Support: IE 9-11 only
      // IE 9-11 do not support classes and IE9 leaks with the code below.
      if (msie || typeof func !== 'function') {
        return false;
      }
      var result = func.$$ngIsClass;
      if (!isBoolean(result)) {
        // Support: Edge 12-13 only
        // See: https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/6156135/
        result = func.$$ngIsClass = /^(?:class\b|constructor\()/.test(stringifyFn(func));
      }
      return result;
    }

    function invoke(fn, self, locals, serviceName) {
      if (typeof locals === 'string') {
        serviceName = locals;
        locals = null;
      }

      var args = injectionArgs(fn, locals, serviceName);
      if (isArray(fn)) {
        fn = fn[fn.length - 1];
      }

      if (!isClass(fn)) {
        // http://jsperf.com/angularjs-invoke-apply-vs-switch
        // #5388
        return fn.apply(self, args);
      } else {
        args.unshift(null);
        return new (Function.prototype.bind.apply(fn, args))();
      }
    }


    function instantiate(Type, locals, serviceName) {
      // Check if Type is annotated and use just the given function at n-1 as parameter
      // e.g. someModule.factory('greeter', ['$window', function(renamed$window) {}]);
      var ctor = (isArray(Type) ? Type[Type.length - 1] : Type);
      var args = injectionArgs(Type, locals, serviceName);
      // Empty object at position 0 is ignored for invocation with `new`, but required.
      args.unshift(null);
      return new (Function.prototype.bind.apply(ctor, args))();
    }


    return {
      invoke: invoke,
      instantiate: instantiate,
      get: getService,
      annotate: createInjector.$$annotate,
      has: function(name) {
        return providerCache.hasOwnProperty(name + providerSuffix) || cache.hasOwnProperty(name);
      }
    };
  }
}

createInjector.$$annotate = annotate;

/**
 * @ngdoc provider
 * @name $anchorScrollProvider
 * @this
 *
 * @description
 * Use `$anchorScrollProvider` to disable automatic scrolling whenever
 * {@link ng.$location#hash $location.hash()} changes.
 */
function $AnchorScrollProvider() {

  var autoScrollingEnabled = true;

  /**
   * @ngdoc method
   * @name $anchorScrollProvider#disableAutoScrolling
   *
   * @description
   * By default, {@link ng.$anchorScroll $anchorScroll()} will automatically detect changes to
   * {@link ng.$location#hash $location.hash()} and scroll to the element matching the new hash.<br />
   * Use this method to disable automatic scrolling.
   *
   * If automatic scrolling is disabled, one must explicitly call
   * {@link ng.$anchorScroll $anchorScroll()} in order to scroll to the element related to the
   * current hash.
   */
  this.disableAutoScrolling = function() {
    autoScrollingEnabled = false;
  };

  /**
   * @ngdoc service
   * @name $anchorScroll
   * @kind function
   * @requires $window
   * @requires $location
   * @requires $rootScope
   *
   * @description
   * When called, it scrolls to the element related to the specified `hash` or (if omitted) to the
   * current value of {@link ng.$location#hash $location.hash()}, according to the rules specified
   * in the
   * [HTML5 spec](http://www.w3.org/html/wg/drafts/html/master/browsers.html#an-indicated-part-of-the-document).
   *
   * It also watches the {@link ng.$location#hash $location.hash()} and automatically scrolls to
   * match any anchor whenever it changes. This can be disabled by calling
   * {@link ng.$anchorScrollProvider#disableAutoScrolling $anchorScrollProvider.disableAutoScrolling()}.
   *
   * Additionally, you can use its {@link ng.$anchorScroll#yOffset yOffset} property to specify a
   * vertical scroll-offset (either fixed or dynamic).
   *
   * @param {string=} hash The hash specifying the element to scroll to. If omitted, the value of
   *                       {@link ng.$location#hash $location.hash()} will be used.
   *
   * @property {(number|function|jqLite)} yOffset
   * If set, specifies a vertical scroll-offset. This is often useful when there are fixed
   * positioned elements at the top of the page, such as navbars, headers etc.
   *
   * `yOffset` can be specified in various ways:
   * - **number**: A fixed number of pixels to be used as offset.<br /><br />
   * - **function**: A getter function called everytime `$anchorScroll()` is executed. Must return
   *   a number representing the offset (in pixels).<br /><br />
   * - **jqLite**: A jqLite/jQuery element to be used for specifying the offset. The distance from
   *   the top of the page to the element's bottom will be used as offset.<br />
   *   **Note**: The element will be taken into account only as long as its `position` is set to
   *   `fixed`. This option is useful, when dealing with responsive navbars/headers that adjust
   *   their height and/or positioning according to the viewport's size.
   *
   * <br />
   * <div class="alert alert-warning">
   * In order for `yOffset` to work properly, scrolling should take place on the document's root and
   * not some child element.
   * </div>
   *
   * @example
     <example module="anchorScrollExample" name="anchor-scroll">
       <file name="index.html">
         <div id="scrollArea" ng-controller="ScrollController">
           <a ng-click="gotoBottom()">Go to bottom</a>
           <a id="bottom"></a> You're at the bottom!
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollExample', [])
           .controller('ScrollController', ['$scope', '$location', '$anchorScroll',
             function($scope, $location, $anchorScroll) {
               $scope.gotoBottom = function() {
                 // set the location.hash to the id of
                 // the element you wish to scroll to.
                 $location.hash('bottom');

                 // call $anchorScroll()
                 $anchorScroll();
               };
             }]);
       </file>
       <file name="style.css">
         #scrollArea {
           height: 280px;
           overflow: auto;
         }

         #bottom {
           display: block;
           margin-top: 2000px;
         }
       </file>
     </example>
   *
   * <hr />
   * The example below illustrates the use of a vertical scroll-offset (specified as a fixed value).
   * See {@link ng.$anchorScroll#yOffset $anchorScroll.yOffset} for more details.
   *
   * @example
     <example module="anchorScrollOffsetExample" name="anchor-scroll-offset">
       <file name="index.html">
         <div class="fixed-header" ng-controller="headerCtrl">
           <a href="" ng-click="gotoAnchor(x)" ng-repeat="x in [1,2,3,4,5]">
             Go to anchor {{x}}
           </a>
         </div>
         <div id="anchor{{x}}" class="anchor" ng-repeat="x in [1,2,3,4,5]">
           Anchor {{x}} of 5
         </div>
       </file>
       <file name="script.js">
         angular.module('anchorScrollOffsetExample', [])
           .run(['$anchorScroll', function($anchorScroll) {
             $anchorScroll.yOffset = 50;   // always scroll by 50 extra pixels
           }])
           .controller('headerCtrl', ['$anchorScroll', '$location', '$scope',
             function($anchorScroll, $location, $scope) {
               $scope.gotoAnchor = function(x) {
                 var newHash = 'anchor' + x;
                 if ($location.hash() !== newHash) {
                   // set the $location.hash to `newHash` and
                   // $anchorScroll will automatically scroll to it
                   $location.hash('anchor' + x);
                 } else {
                   // call $anchorScroll() explicitly,
                   // since $location.hash hasn't changed
                   $anchorScroll();
                 }
               };
             }
           ]);
       </file>
       <file name="style.css">
         body {
           padding-top: 50px;
         }

         .anchor {
           border: 2px dashed DarkOrchid;
           padding: 10px 10px 200px 10px;
         }

         .fixed-header {
           background-color: rgba(0, 0, 0, 0.2);
           height: 50px;
           position: fixed;
           top: 0; left: 0; right: 0;
         }

         .fixed-header > a {
           display: inline-block;
           margin: 5px 15px;
         }
       </file>
     </example>
   */
  this.$get = ['$window', '$location', '$rootScope', function($window, $location, $rootScope) {
    var document = $window.document;

    // Helper function to get first anchor from a NodeList
    // (using `Array#some()` instead of `angular#forEach()` since it's more performant
    //  and working in all supported browsers.)
    function getFirstAnchor(list) {
      var result = null;
      Array.prototype.some.call(list, function(element) {
        if (nodeName_(element) === 'a') {
          result = element;
          return true;
        }
      });
      return result;
    }

    function getYOffset() {

      var offset = scroll.yOffset;

      if (isFunction(offset)) {
        offset = offset();
      } else if (isElement(offset)) {
        var elem = offset[0];
        var style = $window.getComputedStyle(elem);
        if (style.position !== 'fixed') {
          offset = 0;
        } else {
          offset = elem.getBoundingClientRect().bottom;
        }
      } else if (!isNumber(offset)) {
        offset = 0;
      }

      return offset;
    }

    function scrollTo(elem) {
      if (elem) {
        elem.scrollIntoView();

        var offset = getYOffset();

        if (offset) {
          // `offset` is the number of pixels we should scroll UP in order to align `elem` properly.
          // This is true ONLY if the call to `elem.scrollIntoView()` initially aligns `elem` at the
          // top of the viewport.
          //
          // IF the number of pixels from the top of `elem` to the end of the page's content is less
          // than the height of the viewport, then `elem.scrollIntoView()` will align the `elem` some
          // way down the page.
          //
          // This is often the case for elements near the bottom of the page.
          //
          // In such cases we do not need to scroll the whole `offset` up, just the difference between
          // the top of the element and the offset, which is enough to align the top of `elem` at the
          // desired position.
          var elemTop = elem.getBoundingClientRect().top;
          $window.scrollBy(0, elemTop - offset);
        }
      } else {
        $window.scrollTo(0, 0);
      }
    }

    function scroll(hash) {
      // Allow numeric hashes
      hash = isString(hash) ? hash : isNumber(hash) ? hash.toString() : $location.hash();
      var elm;

      // empty hash, scroll to the top of the page
      if (!hash) scrollTo(null);

      // element with given id
      else if ((elm = document.getElementById(hash))) scrollTo(elm);

      // first anchor with given name :-D
      else if ((elm = getFirstAnchor(document.getElementsByName(hash)))) scrollTo(elm);

      // no element and hash === 'top', scroll to the top of the page
      else if (hash === 'top') scrollTo(null);
    }

    // does not scroll when user clicks on anchor link that is currently on
    // (no url change, no $location.hash() change), browser native does scroll
    if (autoScrollingEnabled) {
      $rootScope.$watch(function autoScrollWatch() {return $location.hash();},
        function autoScrollWatchAction(newVal, oldVal) {
          // skip the initial scroll if $location.hash is empty
          if (newVal === oldVal && newVal === '') return;

          jqLiteDocumentLoaded(function() {
            $rootScope.$evalAsync(scroll);
          });
        });
    }

    return scroll;
  }];
}

var $animateMinErr = minErr('$animate');
var ELEMENT_NODE = 1;
var NG_ANIMATE_CLASSNAME = 'ng-animate';

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function extractElementNode(element) {
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function splitClasses(classes) {
  if (isString(classes)) {
    classes = classes.split(' ');
  }

  // Use createMap() to prevent class assumptions involving property names in
  // Object.prototype
  var obj = createMap();
  forEach(classes, function(klass) {
    // sometimes the split leaves empty string values
    // incase extra spaces were applied to the options
    if (klass.length) {
      obj[klass] = true;
    }
  });
  return obj;
}

// if any other type of options value besides an Object value is
// passed into the $animate.method() animation then this helper code
// will be run which will ignore it. While this patch is not the
// greatest solution to this, a lot of existing plugins depend on
// $animate to either call the callback (< 1.2) or return a promise
// that can be changed. This helper function ensures that the options
// are wiped clean incase a callback function is provided.
function prepareAnimateOptions(options) {
  return isObject(options)
      ? options
      : {};
}

var $$CoreAnimateJsProvider = /** @this */ function() {
  this.$get = noop;
};

// this is prefixed with Core since it conflicts with
// the animateQueueProvider defined in ngAnimate/animateQueue.js
var $$CoreAnimateQueueProvider = /** @this */ function() {
  var postDigestQueue = new NgMap();
  var postDigestElements = [];

  this.$get = ['$$AnimateRunner', '$rootScope',
       function($$AnimateRunner,   $rootScope) {
    return {
      enabled: noop,
      on: noop,
      off: noop,
      pin: noop,

      push: function(element, event, options, domOperation) {
        if (domOperation) {
          domOperation();
        }

        options = options || {};
        if (options.from) {
          element.css(options.from);
        }
        if (options.to) {
          element.css(options.to);
        }

        if (options.addClass || options.removeClass) {
          addRemoveClassesPostDigest(element, options.addClass, options.removeClass);
        }

        var runner = new $$AnimateRunner();

        // since there are no animations to run the runner needs to be
        // notified that the animation call is complete.
        runner.complete();
        return runner;
      }
    };


    function updateData(data, classes, value) {
      var changed = false;
      if (classes) {
        classes = isString(classes) ? classes.split(' ') :
                  isArray(classes) ? classes : [];
        forEach(classes, function(className) {
          if (className) {
            changed = true;
            data[className] = value;
          }
        });
      }
      return changed;
    }

    function handleCSSClassChanges() {
      forEach(postDigestElements, function(element) {
        var data = postDigestQueue.get(element);
        if (data) {
          var existing = splitClasses(element.attr('class'));
          var toAdd = '';
          var toRemove = '';
          forEach(data, function(status, className) {
            var hasClass = !!existing[className];
            if (status !== hasClass) {
              if (status) {
                toAdd += (toAdd.length ? ' ' : '') + className;
              } else {
                toRemove += (toRemove.length ? ' ' : '') + className;
              }
            }
          });

          forEach(element, function(elm) {
            if (toAdd) {
              jqLiteAddClass(elm, toAdd);
            }
            if (toRemove) {
              jqLiteRemoveClass(elm, toRemove);
            }
          });
          postDigestQueue.delete(element);
        }
      });
      postDigestElements.length = 0;
    }


    function addRemoveClassesPostDigest(element, add, remove) {
      var data = postDigestQueue.get(element) || {};

      var classesAdded = updateData(data, add, true);
      var classesRemoved = updateData(data, remove, false);

      if (classesAdded || classesRemoved) {

        postDigestQueue.set(element, data);
        postDigestElements.push(element);

        if (postDigestElements.length === 1) {
          $rootScope.$$postDigest(handleCSSClassChanges);
        }
      }
    }
  }];
};

/**
 * @ngdoc provider
 * @name $animateProvider
 *
 * @description
 * Default implementation of $animate that doesn't perform any animations, instead just
 * synchronously performs DOM updates and resolves the returned runner promise.
 *
 * In order to enable animations the `ngAnimate` module has to be loaded.
 *
 * To see the functional implementation check out `src/ngAnimate/animate.js`.
 */
var $AnimateProvider = ['$provide', /** @this */ function($provide) {
  var provider = this;
  var classNameFilter = null;
  var customFilter = null;

  this.$$registeredAnimations = Object.create(null);

   /**
   * @ngdoc method
   * @name $animateProvider#register
   *
   * @description
   * Registers a new injectable animation factory function. The factory function produces the
   * animation object which contains callback functions for each event that is expected to be
   * animated.
   *
   *   * `eventFn`: `function(element, ... , doneFunction, options)`
   *   The element to animate, the `doneFunction` and the options fed into the animation. Depending
   *   on the type of animation additional arguments will be injected into the animation function. The
   *   list below explains the function signatures for the different animation methods:
   *
   *   - setClass: function(element, addedClasses, removedClasses, doneFunction, options)
   *   - addClass: function(element, addedClasses, doneFunction, options)
   *   - removeClass: function(element, removedClasses, doneFunction, options)
   *   - enter, leave, move: function(element, doneFunction, options)
   *   - animate: function(element, fromStyles, toStyles, doneFunction, options)
   *
   *   Make sure to trigger the `doneFunction` once the animation is fully complete.
   *
   * ```js
   *   return {
   *     //enter, leave, move signature
   *     eventFn : function(element, done, options) {
   *       //code to run the animation
   *       //once complete, then run done()
   *       return function endFunction(wasCancelled) {
   *         //code to cancel the animation
   *       }
   *     }
   *   }
   * ```
   *
   * @param {string} name The name of the animation (this is what the class-based CSS value will be compared to).
   * @param {Function} factory The factory function that will be executed to return the animation
   *                           object.
   */
  this.register = function(name, factory) {
    if (name && name.charAt(0) !== '.') {
      throw $animateMinErr('notcsel', 'Expecting class selector starting with \'.\' got \'{0}\'.', name);
    }

    var key = name + '-animation';
    provider.$$registeredAnimations[name.substr(1)] = key;
    $provide.factory(key, factory);
  };

  /**
   * @ngdoc method
   * @name $animateProvider#customFilter
   *
   * @description
   * Sets and/or returns the custom filter function that is used to "filter" animations, i.e.
   * determine if an animation is allowed or not. When no filter is specified (the default), no
   * animation will be blocked. Setting the `customFilter` value will only allow animations for
   * which the filter function's return value is truthy.
   *
   * This allows to easily create arbitrarily complex rules for filtering animations, such as
   * allowing specific events only, or enabling animations on specific subtrees of the DOM, etc.
   * Filtering animations can also boost performance for low-powered devices, as well as
   * applications containing a lot of structural operations.
   *
   * <div class="alert alert-success">
   *   **Best Practice:**
   *   Keep the filtering function as lean as possible, because it will be called for each DOM
   *   action (e.g. insertion, removal, class change) performed by "animation-aware" directives.
   *   See {@link guide/animations#which-directives-support-animations- here} for a list of built-in
   *   directives that support animations.
   *   Performing computationally expensive or time-consuming operations on each call of the
   *   filtering function can make your animations sluggish.
   * </div>
   *
   * **Note:** If present, `customFilter` will be checked before
   * {@link $animateProvider#classNameFilter classNameFilter}.
   *
   * @param {Function=} filterFn - The filter function which will be used to filter all animations.
   *   If a falsy value is returned, no animation will be performed. The function will be called
   *   with the following arguments:
   *   - **node** `{DOMElement}` - The DOM element to be animated.
   *   - **event** `{String}` - The name of the animation event (e.g. `enter`, `leave`, `addClass`
   *     etc).
   *   - **options** `{Object}` - A collection of options/styles used for the animation.
   * @return {Function} The current filter function or `null` if there is none set.
   */
  this.customFilter = function(filterFn) {
    if (arguments.length === 1) {
      customFilter = isFunction(filterFn) ? filterFn : null;
    }

    return customFilter;
  };

  /**
   * @ngdoc method
   * @name $animateProvider#classNameFilter
   *
   * @description
   * Sets and/or returns the CSS class regular expression that is checked when performing
   * an animation. Upon bootstrap the classNameFilter value is not set at all and will
   * therefore enable $animate to attempt to perform an animation on any element that is triggered.
   * When setting the `classNameFilter` value, animations will only be performed on elements
   * that successfully match the filter expression. This in turn can boost performance
   * for low-powered devices as well as applications containing a lot of structural operations.
   *
   * **Note:** If present, `classNameFilter` will be checked after
   * {@link $animateProvider#customFilter customFilter}. If `customFilter` is present and returns
   * false, `classNameFilter` will not be checked.
   *
   * @param {RegExp=} expression The className expression which will be checked against all animations
   * @return {RegExp} The current CSS className expression value. If null then there is no expression value
   */
  this.classNameFilter = function(expression) {
    if (arguments.length === 1) {
      classNameFilter = (expression instanceof RegExp) ? expression : null;
      if (classNameFilter) {
        var reservedRegex = new RegExp('[(\\s|\\/)]' + NG_ANIMATE_CLASSNAME + '[(\\s|\\/)]');
        if (reservedRegex.test(classNameFilter.toString())) {
          classNameFilter = null;
          throw $animateMinErr('nongcls', '$animateProvider.classNameFilter(regex) prohibits accepting a regex value which matches/contains the "{0}" CSS class.', NG_ANIMATE_CLASSNAME);
        }
      }
    }
    return classNameFilter;
  };

  this.$get = ['$$animateQueue', function($$animateQueue) {
    function domInsert(element, parentElement, afterElement) {
      // if for some reason the previous element was removed
      // from the dom sometime before this code runs then let's
      // just stick to using the parent element as the anchor
      if (afterElement) {
        var afterNode = extractElementNode(afterElement);
        if (afterNode && !afterNode.parentNode && !afterNode.previousElementSibling) {
          afterElement = null;
        }
      }
      if (afterElement) {
        afterElement.after(element);
      } else {
        parentElement.prepend(element);
      }
    }

    /**
     * @ngdoc service
     * @name $animate
     * @description The $animate service exposes a series of DOM utility methods that provide support
     * for animation hooks. The default behavior is the application of DOM operations, however,
     * when an animation is detected (and animations are enabled), $animate will do the heavy lifting
     * to ensure that animation runs with the triggered DOM operation.
     *
     * By default $animate doesn't trigger any animations. This is because the `ngAnimate` module isn't
     * included and only when it is active then the animation hooks that `$animate` triggers will be
     * functional. Once active then all structural `ng-` directives will trigger animations as they perform
     * their DOM-related operations (enter, leave and move). Other directives such as `ngClass`,
     * `ngShow`, `ngHide` and `ngMessages` also provide support for animations.
     *
     * It is recommended that the`$animate` service is always used when executing DOM-related procedures within directives.
     *
     * To learn more about enabling animation support, click here to visit the
     * {@link ngAnimate ngAnimate module page}.
     */
    return {
      // we don't call it directly since non-existant arguments may
      // be interpreted as null within the sub enabled function

      /**
       *
       * @ngdoc method
       * @name $animate#on
       * @kind function
       * @description Sets up an event listener to fire whenever the animation event (enter, leave, move, etc...)
       *    has fired on the given element or among any of its children. Once the listener is fired, the provided callback
       *    is fired with the following params:
       *
       * ```js
       * $animate.on('enter', container,
       *    function callback(element, phase) {
       *      // cool we detected an enter animation within the container
       *    }
       * );
       * ```
       *
       * @param {string} event the animation event that will be captured (e.g. enter, leave, move, addClass, removeClass, etc...)
       * @param {DOMElement} container the container element that will capture each of the animation events that are fired on itself
       *     as well as among its children
       * @param {Function} callback the callback function that will be fired when the listener is triggered
       *
       * The arguments present in the callback function are:
       * * `element` - The captured DOM element that the animation was fired on.
       * * `phase` - The phase of the animation. The two possible phases are **start** (when the animation starts) and **close** (when it ends).
       */
      on: $$animateQueue.on,

      /**
       *
       * @ngdoc method
       * @name $animate#off
       * @kind function
       * @description Deregisters an event listener based on the event which has been associated with the provided element. This method
       * can be used in three different ways depending on the arguments:
       *
       * ```js
       * // remove all the animation event listeners listening for `enter`
       * $animate.off('enter');
       *
       * // remove listeners for all animation events from the container element
       * $animate.off(container);
       *
       * // remove all the animation event listeners listening for `enter` on the given element and its children
       * $animate.off('enter', container);
       *
       * // remove the event listener function provided by `callback` that is set
       * // to listen for `enter` on the given `container` as well as its children
       * $animate.off('enter', container, callback);
       * ```
       *
       * @param {string|DOMElement} event|container the animation event (e.g. enter, leave, move,
       * addClass, removeClass, etc...), or the container element. If it is the element, all other
       * arguments are ignored.
       * @param {DOMElement=} container the container element the event listener was placed on
       * @param {Function=} callback the callback function that was registered as the listener
       */
      off: $$animateQueue.off,

      /**
       * @ngdoc method
       * @name $animate#pin
       * @kind function
       * @description Associates the provided element with a host parent element to allow the element to be animated even if it exists
       *    outside of the DOM structure of the Angular application. By doing so, any animation triggered via `$animate` can be issued on the
       *    element despite being outside the realm of the application or within another application. Say for example if the application
       *    was bootstrapped on an element that is somewhere inside of the `<body>` tag, but we wanted to allow for an element to be situated
       *    as a direct child of `document.body`, then this can be achieved by pinning the element via `$animate.pin(element)`. Keep in mind
       *    that calling `$animate.pin(element, parentElement)` will not actually insert into the DOM anywhere; it will just create the association.
       *
       *    Note that this feature is only active when the `ngAnimate` module is used.
       *
       * @param {DOMElement} element the external element that will be pinned
       * @param {DOMElement} parentElement the host parent element that will be associated with the external element
       */
      pin: $$animateQueue.pin,

      /**
       *
       * @ngdoc method
       * @name $animate#enabled
       * @kind function
       * @description Used to get and set whether animations are enabled or not on the entire application or on an element and its children. This
       * function can be called in four ways:
       *
       * ```js
       * // returns true or false
       * $animate.enabled();
       *
       * // changes the enabled state for all animations
       * $animate.enabled(false);
       * $animate.enabled(true);
       *
       * // returns true or false if animations are enabled for an element
       * $animate.enabled(element);
       *
       * // changes the enabled state for an element and its children
       * $animate.enabled(element, true);
       * $animate.enabled(element, false);
       * ```
       *
       * @param {DOMElement=} element the element that will be considered for checking/setting the enabled state
       * @param {boolean=} enabled whether or not the animations will be enabled for the element
       *
       * @return {boolean} whether or not animations are enabled
       */
      enabled: $$animateQueue.enabled,

      /**
       * @ngdoc method
       * @name $animate#cancel
       * @kind function
       * @description Cancels the provided animation.
       *
       * @param {Promise} animationPromise The animation promise that is returned when an animation is started.
       */
      cancel: function(runner) {
        if (runner.end) {
          runner.end();
        }
      },

      /**
       *
       * @ngdoc method
       * @name $animate#enter
       * @kind function
       * @description Inserts the element into the DOM either after the `after` element (if provided) or
       *   as the first child within the `parent` element and then triggers an animation.
       *   A promise is returned that will be resolved during the next digest once the animation
       *   has completed.
       *
       * @param {DOMElement} element the element which will be inserted into the DOM
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      enter: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'enter', prepareAnimateOptions(options));
      },

      /**
       *
       * @ngdoc method
       * @name $animate#move
       * @kind function
       * @description Inserts (moves) the element into its new position in the DOM either after
       *   the `after` element (if provided) or as the first child within the `parent` element
       *   and then triggers an animation. A promise is returned that will be resolved
       *   during the next digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be moved into the new DOM position
       * @param {DOMElement} parent the parent element which will append the element as
       *   a child (so long as the after element is not present)
       * @param {DOMElement=} after the sibling element after which the element will be appended
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      move: function(element, parent, after, options) {
        parent = parent && jqLite(parent);
        after = after && jqLite(after);
        parent = parent || after.parent();
        domInsert(element, parent, after);
        return $$animateQueue.push(element, 'move', prepareAnimateOptions(options));
      },

      /**
       * @ngdoc method
       * @name $animate#leave
       * @kind function
       * @description Triggers an animation and then removes the element from the DOM.
       * When the function is called a promise is returned that will be resolved during the next
       * digest once the animation has completed.
       *
       * @param {DOMElement} element the element which will be removed from the DOM
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      leave: function(element, options) {
        return $$animateQueue.push(element, 'leave', prepareAnimateOptions(options), function() {
          element.remove();
        });
      },

      /**
       * @ngdoc method
       * @name $animate#addClass
       * @kind function
       *
       * @description Triggers an addClass animation surrounding the addition of the provided CSS class(es). Upon
       *   execution, the addClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element already contains the CSS class or if the class is removed at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      addClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addclass, className);
        return $$animateQueue.push(element, 'addClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#removeClass
       * @kind function
       *
       * @description Triggers a removeClass animation surrounding the removal of the provided CSS class(es). Upon
       *   execution, the removeClass operation will only be handled after the next digest and it will not trigger an
       *   animation if element does not contain the CSS class or if the class is added at a later step.
       *   Note that class-based animations are treated differently compared to structural animations
       *   (like enter, move and leave) since the CSS classes may be added/removed at different points
       *   depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} className the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      removeClass: function(element, className, options) {
        options = prepareAnimateOptions(options);
        options.removeClass = mergeClasses(options.removeClass, className);
        return $$animateQueue.push(element, 'removeClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#setClass
       * @kind function
       *
       * @description Performs both the addition and removal of a CSS classes on an element and (during the process)
       *    triggers an animation surrounding the class addition/removal. Much like `$animate.addClass` and
       *    `$animate.removeClass`, `setClass` will only evaluate the classes being added/removed once a digest has
       *    passed. Note that class-based animations are treated differently compared to structural animations
       *    (like enter, move and leave) since the CSS classes may be added/removed at different points
       *    depending if CSS or JavaScript animations are used.
       *
       * @param {DOMElement} element the element which the CSS classes will be applied to
       * @param {string} add the CSS class(es) that will be added (multiple classes are separated via spaces)
       * @param {string} remove the CSS class(es) that will be removed (multiple classes are separated via spaces)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      setClass: function(element, add, remove, options) {
        options = prepareAnimateOptions(options);
        options.addClass = mergeClasses(options.addClass, add);
        options.removeClass = mergeClasses(options.removeClass, remove);
        return $$animateQueue.push(element, 'setClass', options);
      },

      /**
       * @ngdoc method
       * @name $animate#animate
       * @kind function
       *
       * @description Performs an inline animation on the element which applies the provided to and from CSS styles to the element.
       * If any detected CSS transition, keyframe or JavaScript matches the provided className value, then the animation will take
       * on the provided styles. For example, if a transition animation is set for the given className, then the provided `from` and
       * `to` styles will be applied alongside the given transition. If the CSS style provided in `from` does not have a corresponding
       * style in `to`, the style in `from` is applied immediately, and no animation is run.
       * If a JavaScript animation is detected then the provided styles will be given in as function parameters into the `animate`
       * method (or as part of the `options` parameter):
       *
       * ```js
       * ngModule.animation('.my-inline-animation', function() {
       *   return {
       *     animate : function(element, from, to, done, options) {
       *       //animation
       *       done();
       *     }
       *   }
       * });
       * ```
       *
       * @param {DOMElement} element the element which the CSS styles will be applied to
       * @param {object} from the from (starting) CSS styles that will be applied to the element and across the animation.
       * @param {object} to the to (destination) CSS styles that will be applied to the element and across the animation.
       * @param {string=} className an optional CSS class that will be applied to the element for the duration of the animation. If
       *    this value is left as empty then a CSS class of `ng-inline-animate` will be applied to the element.
       *    (Note that if no animation is detected then this value will not be applied to the element.)
       * @param {object=} options an optional collection of options/styles that will be applied to the element.
       *   The object can have the following properties:
       *
       *   - **addClass** - `{string}` - space-separated CSS classes to add to element
       *   - **from** - `{Object}` - CSS properties & values at the beginning of animation. Must have matching `to`
       *   - **removeClass** - `{string}` - space-separated CSS classes to remove from element
       *   - **to** - `{Object}` - CSS properties & values at end of animation. Must have matching `from`
       *
       * @return {Promise} the animation callback promise
       */
      animate: function(element, from, to, className, options) {
        options = prepareAnimateOptions(options);
        options.from = options.from ? extend(options.from, from) : from;
        options.to   = options.to   ? extend(options.to, to)     : to;

        className = className || 'ng-inline-animate';
        options.tempClasses = mergeClasses(options.tempClasses, className);
        return $$animateQueue.push(element, 'animate', options);
      }
    };
  }];
}];

var $$AnimateAsyncRunFactoryProvider = /** @this */ function() {
  this.$get = ['$$rAF', function($$rAF) {
    var waitQueue = [];

    function waitForTick(fn) {
      waitQueue.push(fn);
      if (waitQueue.length > 1) return;
      $$rAF(function() {
        for (var i = 0; i < waitQueue.length; i++) {
          waitQueue[i]();
        }
        waitQueue = [];
      });
    }

    return function() {
      var passed = false;
      waitForTick(function() {
        passed = true;
      });
      return function(callback) {
        if (passed) {
          callback();
        } else {
          waitForTick(callback);
        }
      };
    };
  }];
};

var $$AnimateRunnerFactoryProvider = /** @this */ function() {
  this.$get = ['$q', '$sniffer', '$$animateAsyncRun', '$$isDocumentHidden', '$timeout',
       function($q,   $sniffer,   $$animateAsyncRun,   $$isDocumentHidden,   $timeout) {

    var INITIAL_STATE = 0;
    var DONE_PENDING_STATE = 1;
    var DONE_COMPLETE_STATE = 2;

    AnimateRunner.chain = function(chain, callback) {
      var index = 0;

      next();
      function next() {
        if (index === chain.length) {
          callback(true);
          return;
        }

        chain[index](function(response) {
          if (response === false) {
            callback(false);
            return;
          }
          index++;
          next();
        });
      }
    };

    AnimateRunner.all = function(runners, callback) {
      var count = 0;
      var status = true;
      forEach(runners, function(runner) {
        runner.done(onProgress);
      });

      function onProgress(response) {
        status = status && response;
        if (++count === runners.length) {
          callback(status);
        }
      }
    };

    function AnimateRunner(host) {
      this.setHost(host);

      var rafTick = $$animateAsyncRun();
      var timeoutTick = function(fn) {
        $timeout(fn, 0, false);
      };

      this._doneCallbacks = [];
      this._tick = function(fn) {
        if ($$isDocumentHidden()) {
          timeoutTick(fn);
        } else {
          rafTick(fn);
        }
      };
      this._state = 0;
    }

    AnimateRunner.prototype = {
      setHost: function(host) {
        this.host = host || {};
      },

      done: function(fn) {
        if (this._state === DONE_COMPLETE_STATE) {
          fn();
        } else {
          this._doneCallbacks.push(fn);
        }
      },

      progress: noop,

      getPromise: function() {
        if (!this.promise) {
          var self = this;
          this.promise = $q(function(resolve, reject) {
            self.done(function(status) {
              if (status === false) {
                reject();
              } else {
                resolve();
              }
            });
          });
        }
        return this.promise;
      },

      then: function(resolveHandler, rejectHandler) {
        return this.getPromise().then(resolveHandler, rejectHandler);
      },

      'catch': function(handler) {
        return this.getPromise()['catch'](handler);
      },

      'finally': function(handler) {
        return this.getPromise()['finally'](handler);
      },

      pause: function() {
        if (this.host.pause) {
          this.host.pause();
        }
      },

      resume: function() {
        if (this.host.resume) {
          this.host.resume();
        }
      },

      end: function() {
        if (this.host.end) {
          this.host.end();
        }
        this._resolve(true);
      },

      cancel: function() {
        if (this.host.cancel) {
          this.host.cancel();
        }
        this._resolve(false);
      },

      complete: function(response) {
        var self = this;
        if (self._state === INITIAL_STATE) {
          self._state = DONE_PENDING_STATE;
          self._tick(function() {
            self._resolve(response);
          });
        }
      },

      _resolve: function(response) {
        if (this._state !== DONE_COMPLETE_STATE) {
          forEach(this._doneCallbacks, function(fn) {
            fn(response);
          });
          this._doneCallbacks.length = 0;
          this._state = DONE_COMPLETE_STATE;
        }
      }
    };

    return AnimateRunner;
  }];
};

/* exported $CoreAnimateCssProvider */

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 * @this
 *
 * @description
 * This is the core version of `$animateCss`. By default, only when the `ngAnimate` is included,
 * then the `$animateCss` service will actually perform animations.
 *
 * Click here {@link ngAnimate.$animateCss to read the documentation for $animateCss}.
 */
var $CoreAnimateCssProvider = function() {
  this.$get = ['$$rAF', '$q', '$$AnimateRunner', function($$rAF, $q, $$AnimateRunner) {

    return function(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = copy(options);
      }

      // there is no point in applying the styles since
      // there is no animation that goes on at all in
      // this version of $animateCss.
      if (options.cleanupStyles) {
        options.from = options.to = null;
      }

      if (options.from) {
        element.css(options.from);
        options.from = null;
      }

      var closed, runner = new $$AnimateRunner();
      return {
        start: run,
        end: run
      };

      function run() {
        $$rAF(function() {
          applyAnimationContents();
          if (!closed) {
            runner.complete();
          }
          closed = true;
        });
        return runner;
      }

      function applyAnimationContents() {
        if (options.addClass) {
          element.addClass(options.addClass);
          options.addClass = null;
        }
        if (options.removeClass) {
          element.removeClass(options.removeClass);
          options.removeClass = null;
        }
        if (options.to) {
          element.css(options.to);
          options.to = null;
        }
      }
    };
  }];
};

/* global stripHash: true */

/**
 * ! This is a private undocumented service !
 *
 * @name $browser
 * @requires $log
 * @description
 * This object has two goals:
 *
 * - hide all the global state in the browser caused by the window object
 * - abstract away all the browser specific features and inconsistencies
 *
 * For tests we provide {@link ngMock.$browser mock implementation} of the `$browser`
 * service, which can be used for convenient testing of the application without the interaction with
 * the real browser apis.
 */
/**
 * @param {object} window The global window object.
 * @param {object} document jQuery wrapped document.
 * @param {object} $log window.console or an object with the same interface.
 * @param {object} $sniffer $sniffer service
 */
function Browser(window, document, $log, $sniffer) {
  var self = this,
      location = window.location,
      history = window.history,
      setTimeout = window.setTimeout,
      clearTimeout = window.clearTimeout,
      pendingDeferIds = {};

  self.isMock = false;

  var outstandingRequestCount = 0;
  var outstandingRequestCallbacks = [];

  // TODO(vojta): remove this temporary api
  self.$$completeOutstandingRequest = completeOutstandingRequest;
  self.$$incOutstandingRequestCount = function() { outstandingRequestCount++; };

  /**
   * Executes the `fn` function(supports currying) and decrements the `outstandingRequestCallbacks`
   * counter. If the counter reaches 0, all the `outstandingRequestCallbacks` are executed.
   */
  function completeOutstandingRequest(fn) {
    try {
      fn.apply(null, sliceArgs(arguments, 1));
    } finally {
      outstandingRequestCount--;
      if (outstandingRequestCount === 0) {
        while (outstandingRequestCallbacks.length) {
          try {
            outstandingRequestCallbacks.pop()();
          } catch (e) {
            $log.error(e);
          }
        }
      }
    }
  }

  function getHash(url) {
    var index = url.indexOf('#');
    return index === -1 ? '' : url.substr(index);
  }

  /**
   * @private
   * Note: this method is used only by scenario runner
   * TODO(vojta): prefix this method with $$ ?
   * @param {function()} callback Function that will be called when no outstanding request
   */
  self.notifyWhenNoOutstandingRequests = function(callback) {
    if (outstandingRequestCount === 0) {
      callback();
    } else {
      outstandingRequestCallbacks.push(callback);
    }
  };

  //////////////////////////////////////////////////////////////
  // URL API
  //////////////////////////////////////////////////////////////

  var cachedState, lastHistoryState,
      lastBrowserUrl = location.href,
      baseElement = document.find('base'),
      pendingLocation = null,
      getCurrentState = !$sniffer.history ? noop : function getCurrentState() {
        try {
          return history.state;
        } catch (e) {
          // MSIE can reportedly throw when there is no state (UNCONFIRMED).
        }
      };

  cacheState();

  /**
   * @name $browser#url
   *
   * @description
   * GETTER:
   * Without any argument, this method just returns current value of location.href.
   *
   * SETTER:
   * With at least one argument, this method sets url to new value.
   * If html5 history api supported, pushState/replaceState is used, otherwise
   * location.href/location.replace is used.
   * Returns its own instance to allow chaining
   *
   * NOTE: this api is intended for use only by the $location service. Please use the
   * {@link ng.$location $location service} to change url.
   *
   * @param {string} url New url (when used as setter)
   * @param {boolean=} replace Should new url replace current history record?
   * @param {object=} state object to use with pushState/replaceState
   */
  self.url = function(url, replace, state) {
    // In modern browsers `history.state` is `null` by default; treating it separately
    // from `undefined` would cause `$browser.url('/foo')` to change `history.state`
    // to undefined via `pushState`. Instead, let's change `undefined` to `null` here.
    if (isUndefined(state)) {
      state = null;
    }

    // Android Browser BFCache causes location, history reference to become stale.
    if (location !== window.location) location = window.location;
    if (history !== window.history) history = window.history;

    // setter
    if (url) {
      var sameState = lastHistoryState === state;

      // Don't change anything if previous and current URLs and states match. This also prevents
      // IE<10 from getting into redirect loop when in LocationHashbangInHtml5Url mode.
      // See https://github.com/angular/angular.js/commit/ffb2701
      if (lastBrowserUrl === url && (!$sniffer.history || sameState)) {
        return self;
      }
      var sameBase = lastBrowserUrl && stripHash(lastBrowserUrl) === stripHash(url);
      lastBrowserUrl = url;
      lastHistoryState = state;
      // Don't use history API if only the hash changed
      // due to a bug in IE10/IE11 which leads
      // to not firing a `hashchange` nor `popstate` event
      // in some cases (see #9143).
      if ($sniffer.history && (!sameBase || !sameState)) {
        history[replace ? 'replaceState' : 'pushState'](state, '', url);
        cacheState();
      } else {
        if (!sameBase) {
          pendingLocation = url;
        }
        if (replace) {
          location.replace(url);
        } else if (!sameBase) {
          location.href = url;
        } else {
          location.hash = getHash(url);
        }
        if (location.href !== url) {
          pendingLocation = url;
        }
      }
      if (pendingLocation) {
        pendingLocation = url;
      }
      return self;
    // getter
    } else {
      // - pendingLocation is needed as browsers don't allow to read out
      //   the new location.href if a reload happened or if there is a bug like in iOS 9 (see
      //   https://openradar.appspot.com/22186109).
      // - the replacement is a workaround for https://bugzilla.mozilla.org/show_bug.cgi?id=407172
      return pendingLocation || location.href.replace(/%27/g,'\'');
    }
  };

  /**
   * @name $browser#state
   *
   * @description
   * This method is a getter.
   *
   * Return history.state or null if history.state is undefined.
   *
   * @returns {object} state
   */
  self.state = function() {
    return cachedState;
  };

  var urlChangeListeners = [],
      urlChangeInit = false;

  function cacheStateAndFireUrlChange() {
    pendingLocation = null;
    fireStateOrUrlChange();
  }

  // This variable should be used *only* inside the cacheState function.
  var lastCachedState = null;
  function cacheState() {
    // This should be the only place in $browser where `history.state` is read.
    cachedState = getCurrentState();
    cachedState = isUndefined(cachedState) ? null : cachedState;

    // Prevent callbacks fo fire twice if both hashchange & popstate were fired.
    if (equals(cachedState, lastCachedState)) {
      cachedState = lastCachedState;
    }

    lastCachedState = cachedState;
    lastHistoryState = cachedState;
  }

  function fireStateOrUrlChange() {
    var prevLastHistoryState = lastHistoryState;
    cacheState();

    if (lastBrowserUrl === self.url() && prevLastHistoryState === cachedState) {
      return;
    }

    lastBrowserUrl = self.url();
    lastHistoryState = cachedState;
    forEach(urlChangeListeners, function(listener) {
      listener(self.url(), cachedState);
    });
  }

  /**
   * @name $browser#onUrlChange
   *
   * @description
   * Register callback function that will be called, when url changes.
   *
   * It's only called when the url is changed from outside of angular:
   * - user types different url into address bar
   * - user clicks on history (forward/back) button
   * - user clicks on a link
   *
   * It's not called when url is changed by $browser.url() method
   *
   * The listener gets called with new url as parameter.
   *
   * NOTE: this api is intended for use only by the $location service. Please use the
   * {@link ng.$location $location service} to monitor url changes in angular apps.
   *
   * @param {function(string)} listener Listener function to be called when url changes.
   * @return {function(string)} Returns the registered listener fn - handy if the fn is anonymous.
   */
  self.onUrlChange = function(callback) {
    // TODO(vojta): refactor to use node's syntax for events
    if (!urlChangeInit) {
      // We listen on both (hashchange/popstate) when available, as some browsers don't
      // fire popstate when user changes the address bar and don't fire hashchange when url
      // changed by push/replaceState

      // html5 history api - popstate event
      if ($sniffer.history) jqLite(window).on('popstate', cacheStateAndFireUrlChange);
      // hashchange event
      jqLite(window).on('hashchange', cacheStateAndFireUrlChange);

      urlChangeInit = true;
    }

    urlChangeListeners.push(callback);
    return callback;
  };

  /**
   * @private
   * Remove popstate and hashchange handler from window.
   *
   * NOTE: this api is intended for use only by $rootScope.
   */
  self.$$applicationDestroyed = function() {
    jqLite(window).off('hashchange popstate', cacheStateAndFireUrlChange);
  };

  /**
   * Checks whether the url has changed outside of Angular.
   * Needs to be exported to be able to check for changes that have been done in sync,
   * as hashchange/popstate events fire in async.
   */
  self.$$checkUrlChange = fireStateOrUrlChange;

  //////////////////////////////////////////////////////////////
  // Misc API
  //////////////////////////////////////////////////////////////

  /**
   * @name $browser#baseHref
   *
   * @description
   * Returns current <base href>
   * (always relative - without domain)
   *
   * @returns {string} The current base href
   */
  self.baseHref = function() {
    var href = baseElement.attr('href');
    return href ? href.replace(/^(https?:)?\/\/[^/]*/, '') : '';
  };

  /**
   * @name $browser#defer
   * @param {function()} fn A function, who's execution should be deferred.
   * @param {number=} [delay=0] of milliseconds to defer the function execution.
   * @returns {*} DeferId that can be used to cancel the task via `$browser.defer.cancel()`.
   *
   * @description
   * Executes a fn asynchronously via `setTimeout(fn, delay)`.
   *
   * Unlike when calling `setTimeout` directly, in test this function is mocked and instead of using
   * `setTimeout` in tests, the fns are queued in an array, which can be programmatically flushed
   * via `$browser.defer.flush()`.
   *
   */
  self.defer = function(fn, delay) {
    var timeoutId;
    outstandingRequestCount++;
    timeoutId = setTimeout(function() {
      delete pendingDeferIds[timeoutId];
      completeOutstandingRequest(fn);
    }, delay || 0);
    pendingDeferIds[timeoutId] = true;
    return timeoutId;
  };


  /**
   * @name $browser#defer.cancel
   *
   * @description
   * Cancels a deferred task identified with `deferId`.
   *
   * @param {*} deferId Token returned by the `$browser.defer` function.
   * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
   *                    canceled.
   */
  self.defer.cancel = function(deferId) {
    if (pendingDeferIds[deferId]) {
      delete pendingDeferIds[deferId];
      clearTimeout(deferId);
      completeOutstandingRequest(noop);
      return true;
    }
    return false;
  };

}

/** @this */
function $BrowserProvider() {
  this.$get = ['$window', '$log', '$sniffer', '$document',
      function($window, $log, $sniffer, $document) {
        return new Browser($window, $document, $log, $sniffer);
      }];
}

/**
 * @ngdoc service
 * @name $cacheFactory
 * @this
 *
 * @description
 * Factory that constructs {@link $cacheFactory.Cache Cache} objects and gives access to
 * them.
 *
 * ```js
 *
 *  var cache = $cacheFactory('cacheId');
 *  expect($cacheFactory.get('cacheId')).toBe(cache);
 *  expect($cacheFactory.get('noSuchCacheId')).not.toBeDefined();
 *
 *  cache.put("key", "value");
 *  cache.put("another key", "another value");
 *
 *  // We've specified no options on creation
 *  expect(cache.info()).toEqual({id: 'cacheId', size: 2});
 *
 * ```
 *
 *
 * @param {string} cacheId Name or id of the newly created cache.
 * @param {object=} options Options object that specifies the cache behavior. Properties:
 *
 *   - `{number=}` `capacity` — turns the cache into LRU cache.
 *
 * @returns {object} Newly created cache object with the following set of methods:
 *
 * - `{object}` `info()` — Returns id, size, and options of cache.
 * - `{{*}}` `put({string} key, {*} value)` — Puts a new key-value pair into the cache and returns
 *   it.
 * - `{{*}}` `get({string} key)` — Returns cached value for `key` or undefined for cache miss.
 * - `{void}` `remove({string} key)` — Removes a key-value pair from the cache.
 * - `{void}` `removeAll()` — Removes all cached values.
 * - `{void}` `destroy()` — Removes references to this cache from $cacheFactory.
 *
 * @example
   <example module="cacheExampleApp" name="cache-factory">
     <file name="index.html">
       <div ng-controller="CacheController">
         <input ng-model="newCacheKey" placeholder="Key">
         <input ng-model="newCacheValue" placeholder="Value">
         <button ng-click="put(newCacheKey, newCacheValue)">Cache</button>

         <p ng-if="keys.length">Cached Values</p>
         <div ng-repeat="key in keys">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="cache.get(key)"></b>
         </div>

         <p>Cache Info</p>
         <div ng-repeat="(key, value) in cache.info()">
           <span ng-bind="key"></span>
           <span>: </span>
           <b ng-bind="value"></b>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('cacheExampleApp', []).
         controller('CacheController', ['$scope', '$cacheFactory', function($scope, $cacheFactory) {
           $scope.keys = [];
           $scope.cache = $cacheFactory('cacheId');
           $scope.put = function(key, value) {
             if (angular.isUndefined($scope.cache.get(key))) {
               $scope.keys.push(key);
             }
             $scope.cache.put(key, angular.isUndefined(value) ? null : value);
           };
         }]);
     </file>
     <file name="style.css">
       p {
         margin: 10px 0 3px;
       }
     </file>
   </example>
 */
function $CacheFactoryProvider() {

  this.$get = function() {
    var caches = {};

    function cacheFactory(cacheId, options) {
      if (cacheId in caches) {
        throw minErr('$cacheFactory')('iid', 'CacheId \'{0}\' is already taken!', cacheId);
      }

      var size = 0,
          stats = extend({}, options, {id: cacheId}),
          data = createMap(),
          capacity = (options && options.capacity) || Number.MAX_VALUE,
          lruHash = createMap(),
          freshEnd = null,
          staleEnd = null;

      /**
       * @ngdoc type
       * @name $cacheFactory.Cache
       *
       * @description
       * A cache object used to store and retrieve data, primarily used by
       * {@link $http $http} and the {@link ng.directive:script script} directive to cache
       * templates and other data.
       *
       * ```js
       *  angular.module('superCache')
       *    .factory('superCache', ['$cacheFactory', function($cacheFactory) {
       *      return $cacheFactory('super-cache');
       *    }]);
       * ```
       *
       * Example test:
       *
       * ```js
       *  it('should behave like a cache', inject(function(superCache) {
       *    superCache.put('key', 'value');
       *    superCache.put('another key', 'another value');
       *
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 2
       *    });
       *
       *    superCache.remove('another key');
       *    expect(superCache.get('another key')).toBeUndefined();
       *
       *    superCache.removeAll();
       *    expect(superCache.info()).toEqual({
       *      id: 'super-cache',
       *      size: 0
       *    });
       *  }));
       * ```
       */
      return (caches[cacheId] = {

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#put
         * @kind function
         *
         * @description
         * Inserts a named entry into the {@link $cacheFactory.Cache Cache} object to be
         * retrieved later, and incrementing the size of the cache if the key was not already
         * present in the cache. If behaving like an LRU cache, it will also remove stale
         * entries from the set.
         *
         * It will not insert undefined values into the cache.
         *
         * @param {string} key the key under which the cached data is stored.
         * @param {*} value the value to store alongside the key. If it is undefined, the key
         *    will not be stored.
         * @returns {*} the value stored.
         */
        put: function(key, value) {
          if (isUndefined(value)) return;
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key] || (lruHash[key] = {key: key});

            refresh(lruEntry);
          }

          if (!(key in data)) size++;
          data[key] = value;

          if (size > capacity) {
            this.remove(staleEnd.key);
          }

          return value;
        },

        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#get
         * @kind function
         *
         * @description
         * Retrieves named data stored in the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the data to be retrieved
         * @returns {*} the value stored.
         */
        get: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            refresh(lruEntry);
          }

          return data[key];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#remove
         * @kind function
         *
         * @description
         * Removes an entry from the {@link $cacheFactory.Cache Cache} object.
         *
         * @param {string} key the key of the entry to be removed
         */
        remove: function(key) {
          if (capacity < Number.MAX_VALUE) {
            var lruEntry = lruHash[key];

            if (!lruEntry) return;

            if (lruEntry === freshEnd) freshEnd = lruEntry.p;
            if (lruEntry === staleEnd) staleEnd = lruEntry.n;
            link(lruEntry.n,lruEntry.p);

            delete lruHash[key];
          }

          if (!(key in data)) return;

          delete data[key];
          size--;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#removeAll
         * @kind function
         *
         * @description
         * Clears the cache object of any entries.
         */
        removeAll: function() {
          data = createMap();
          size = 0;
          lruHash = createMap();
          freshEnd = staleEnd = null;
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#destroy
         * @kind function
         *
         * @description
         * Destroys the {@link $cacheFactory.Cache Cache} object entirely,
         * removing it from the {@link $cacheFactory $cacheFactory} set.
         */
        destroy: function() {
          data = null;
          stats = null;
          lruHash = null;
          delete caches[cacheId];
        },


        /**
         * @ngdoc method
         * @name $cacheFactory.Cache#info
         * @kind function
         *
         * @description
         * Retrieve information regarding a particular {@link $cacheFactory.Cache Cache}.
         *
         * @returns {object} an object with the following properties:
         *   <ul>
         *     <li>**id**: the id of the cache instance</li>
         *     <li>**size**: the number of entries kept in the cache instance</li>
         *     <li>**...**: any additional properties from the options object when creating the
         *       cache.</li>
         *   </ul>
         */
        info: function() {
          return extend({}, stats, {size: size});
        }
      });


      /**
       * makes the `entry` the freshEnd of the LRU linked list
       */
      function refresh(entry) {
        if (entry !== freshEnd) {
          if (!staleEnd) {
            staleEnd = entry;
          } else if (staleEnd === entry) {
            staleEnd = entry.n;
          }

          link(entry.n, entry.p);
          link(entry, freshEnd);
          freshEnd = entry;
          freshEnd.n = null;
        }
      }


      /**
       * bidirectionally links two entries of the LRU linked list
       */
      function link(nextEntry, prevEntry) {
        if (nextEntry !== prevEntry) {
          if (nextEntry) nextEntry.p = prevEntry; //p stands for previous, 'prev' didn't minify
          if (prevEntry) prevEntry.n = nextEntry; //n stands for next, 'next' didn't minify
        }
      }
    }


  /**
   * @ngdoc method
   * @name $cacheFactory#info
   *
   * @description
   * Get information about all the caches that have been created
   *
   * @returns {Object} - key-value map of `cacheId` to the result of calling `cache#info`
   */
    cacheFactory.info = function() {
      var info = {};
      forEach(caches, function(cache, cacheId) {
        info[cacheId] = cache.info();
      });
      return info;
    };


  /**
   * @ngdoc method
   * @name $cacheFactory#get
   *
   * @description
   * Get access to a cache object by the `cacheId` used when it was created.
   *
   * @param {string} cacheId Name or id of a cache to access.
   * @returns {object} Cache object identified by the cacheId or undefined if no such cache.
   */
    cacheFactory.get = function(cacheId) {
      return caches[cacheId];
    };


    return cacheFactory;
  };
}

/**
 * @ngdoc service
 * @name $templateCache
 * @this
 *
 * @description
 * The first time a template is used, it is loaded in the template cache for quick retrieval. You
 * can load templates directly into the cache in a `script` tag, or by consuming the
 * `$templateCache` service directly.
 *
 * Adding via the `script` tag:
 *
 * ```html
 *   <script type="text/ng-template" id="templateId.html">
 *     <p>This is the content of the template</p>
 *   </script>
 * ```
 *
 * **Note:** the `script` tag containing the template does not need to be included in the `head` of
 * the document, but it must be a descendent of the {@link ng.$rootElement $rootElement} (IE,
 * element with ng-app attribute), otherwise the template will be ignored.
 *
 * Adding via the `$templateCache` service:
 *
 * ```js
 * var myApp = angular.module('myApp', []);
 * myApp.run(function($templateCache) {
 *   $templateCache.put('templateId.html', 'This is the content of the template');
 * });
 * ```
 *
 * To retrieve the template later, simply use it in your component:
 * ```js
 * myApp.component('myComponent', {
 *    templateUrl: 'templateId.html'
 * });
 * ```
 *
 * or get it via the `$templateCache` service:
 * ```js
 * $templateCache.get('templateId.html')
 * ```
 *
 * See {@link ng.$cacheFactory $cacheFactory}.
 *
 */
function $TemplateCacheProvider() {
  this.$get = ['$cacheFactory', function($cacheFactory) {
    return $cacheFactory('templates');
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables like document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* ! VARIABLE/FUNCTION NAMING CONVENTIONS THAT APPLY TO THIS FILE!
 *
 * DOM-related variables:
 *
 * - "node" - DOM Node
 * - "element" - DOM Element or Node
 * - "$node" or "$element" - jqLite-wrapped node or element
 *
 *
 * Compiler related stuff:
 *
 * - "linkFn" - linking fn of a single directive
 * - "nodeLinkFn" - function that aggregates all linking fns for a particular node
 * - "childLinkFn" -  function that aggregates all linking fns for child nodes of a particular node
 * - "compositeLinkFn" - function that aggregates all linking fns for a compilation root (nodeList)
 */


/**
 * @ngdoc service
 * @name $compile
 * @kind function
 *
 * @description
 * Compiles an HTML string or DOM into a template and produces a template function, which
 * can then be used to link {@link ng.$rootScope.Scope `scope`} and the template together.
 *
 * The compilation is a process of walking the DOM tree and matching DOM elements to
 * {@link ng.$compileProvider#directive directives}.
 *
 * <div class="alert alert-warning">
 * **Note:** This document is an in-depth reference of all directive options.
 * For a gentle introduction to directives with examples of common use cases,
 * see the {@link guide/directive directive guide}.
 * </div>
 *
 * ## Comprehensive Directive API
 *
 * There are many different options for a directive.
 *
 * The difference resides in the return value of the factory function.
 * You can either return a {@link $compile#directive-definition-object Directive Definition Object (see below)}
 * that defines the directive properties, or just the `postLink` function (all other properties will have
 * the default values).
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's recommended to use the "directive definition object" form.
 * </div>
 *
 * Here's an example directive declared with a Directive Definition Object:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       {@link $compile#-priority- priority}: 0,
 *       {@link $compile#-template- template}: '<div></div>', // or // function(tElement, tAttrs) { ... },
 *       // or
 *       // {@link $compile#-templateurl- templateUrl}: 'directive.html', // or // function(tElement, tAttrs) { ... },
 *       {@link $compile#-transclude- transclude}: false,
 *       {@link $compile#-restrict- restrict}: 'A',
 *       {@link $compile#-templatenamespace- templateNamespace}: 'html',
 *       {@link $compile#-scope- scope}: false,
 *       {@link $compile#-controller- controller}: function($scope, $element, $attrs, $transclude, otherInjectables) { ... },
 *       {@link $compile#-controlleras- controllerAs}: 'stringIdentifier',
 *       {@link $compile#-bindtocontroller- bindToController}: false,
 *       {@link $compile#-require- require}: 'siblingDirectiveName', // or // ['^parentDirectiveName', '?optionalDirectiveName', '?^optionalParent'],
 *       {@link $compile#-multielement- multiElement}: false,
 *       {@link $compile#-compile- compile}: function compile(tElement, tAttrs, transclude) {
 *         return {
 *            {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *            {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *         }
 *         // or
 *         // return function postLink( ... ) { ... }
 *       },
 *       // or
 *       // {@link $compile#-link- link}: {
 *       //  {@link $compile#pre-linking-function pre}: function preLink(scope, iElement, iAttrs, controller) { ... },
 *       //  {@link $compile#post-linking-function post}: function postLink(scope, iElement, iAttrs, controller) { ... }
 *       // }
 *       // or
 *       // {@link $compile#-link- link}: function postLink( ... ) { ... }
 *     };
 *     return directiveDefinitionObject;
 *   });
 * ```
 *
 * <div class="alert alert-warning">
 * **Note:** Any unspecified options will use the default value. You can see the default values below.
 * </div>
 *
 * Therefore the above can be simplified as:
 *
 * ```js
 *   var myModule = angular.module(...);
 *
 *   myModule.directive('directiveName', function factory(injectables) {
 *     var directiveDefinitionObject = {
 *       link: function postLink(scope, iElement, iAttrs) { ... }
 *     };
 *     return directiveDefinitionObject;
 *     // or
 *     // return function postLink(scope, iElement, iAttrs) { ... }
 *   });
 * ```
 *
 * ### Life-cycle hooks
 * Directive controllers can provide the following methods that are called by Angular at points in the life-cycle of the
 * directive:
 * * `$onInit()` - Called on each controller after all the controllers on an element have been constructed and
 *   had their bindings initialized (and before the pre &amp; post linking functions for the directives on
 *   this element). This is a good place to put initialization code for your controller.
 * * `$onChanges(changesObj)` - Called whenever one-way (`<`) or interpolation (`@`) bindings are updated. The
 *   `changesObj` is a hash whose keys are the names of the bound properties that have changed, and the values are an
 *   object of the form `{ currentValue, previousValue, isFirstChange() }`. Use this hook to trigger updates within a
 *   component such as cloning the bound value to prevent accidental mutation of the outer value. Note that this will
 *   also be called when your bindings are initialized.
 * * `$doCheck()` - Called on each turn of the digest cycle. Provides an opportunity to detect and act on
 *   changes. Any actions that you wish to take in response to the changes that you detect must be
 *   invoked from this hook; implementing this has no effect on when `$onChanges` is called. For example, this hook
 *   could be useful if you wish to perform a deep equality check, or to check a Date object, changes to which would not
 *   be detected by Angular's change detector and thus not trigger `$onChanges`. This hook is invoked with no arguments;
 *   if detecting changes, you must store the previous value(s) for comparison to the current values.
 * * `$onDestroy()` - Called on a controller when its containing scope is destroyed. Use this hook for releasing
 *   external resources, watches and event handlers. Note that components have their `$onDestroy()` hooks called in
 *   the same order as the `$scope.$broadcast` events are triggered, which is top down. This means that parent
 *   components will have their `$onDestroy()` hook called before child components.
 * * `$postLink()` - Called after this controller's element and its children have been linked. Similar to the post-link
 *   function this hook can be used to set up DOM event handlers and do direct DOM manipulation.
 *   Note that child elements that contain `templateUrl` directives will not have been compiled and linked since
 *   they are waiting for their template to load asynchronously and their own compilation and linking has been
 *   suspended until that occurs.
 *
 * #### Comparison with Angular 2 life-cycle hooks
 * Angular 2 also uses life-cycle hooks for its components. While the Angular 1 life-cycle hooks are similar there are
 * some differences that you should be aware of, especially when it comes to moving your code from Angular 1 to Angular 2:
 *
 * * Angular 1 hooks are prefixed with `$`, such as `$onInit`. Angular 2 hooks are prefixed with `ng`, such as `ngOnInit`.
 * * Angular 1 hooks can be defined on the controller prototype or added to the controller inside its constructor.
 *   In Angular 2 you can only define hooks on the prototype of the Component class.
 * * Due to the differences in change-detection, you may get many more calls to `$doCheck` in Angular 1 than you would to
 *   `ngDoCheck` in Angular 2
 * * Changes to the model inside `$doCheck` will trigger new turns of the digest loop, which will cause the changes to be
 *   propagated throughout the application.
 *   Angular 2 does not allow the `ngDoCheck` hook to trigger a change outside of the component. It will either throw an
 *   error or do nothing depending upon the state of `enableProdMode()`.
 *
 * #### Life-cycle hook examples
 *
 * This example shows how you can check for mutations to a Date object even though the identity of the object
 * has not changed.
 *
 * <example name="doCheckDateExample" module="do-check-module">
 *   <file name="app.js">
 *     angular.module('do-check-module', [])
 *       .component('app', {
 *         template:
 *           'Month: <input ng-model="$ctrl.month" ng-change="$ctrl.updateDate()">' +
 *           'Date: {{ $ctrl.date }}' +
 *           '<test date="$ctrl.date"></test>',
 *         controller: function() {
 *           this.date = new Date();
 *           this.month = this.date.getMonth();
 *           this.updateDate = function() {
 *             this.date.setMonth(this.month);
 *           };
 *         }
 *       })
 *       .component('test', {
 *         bindings: { date: '<' },
 *         template:
 *           '<pre>{{ $ctrl.log | json }}</pre>',
 *         controller: function() {
 *           var previousValue;
 *           this.log = [];
 *           this.$doCheck = function() {
 *             var currentValue = this.date && this.date.valueOf();
 *             if (previousValue !== currentValue) {
 *               this.log.push('doCheck: date mutated: ' + this.date);
 *               previousValue = currentValue;
 *             }
 *           };
 *         }
 *       });
 *   </file>
 *   <file name="index.html">
 *     <app></app>
 *   </file>
 * </example>
 *
 * This example show how you might use `$doCheck` to trigger changes in your component's inputs even if the
 * actual identity of the component doesn't change. (Be aware that cloning and deep equality checks on large
 * arrays or objects can have a negative impact on your application performance)
 *
 * <example name="doCheckArrayExample" module="do-check-module">
 *   <file name="index.html">
 *     <div ng-init="items = []">
 *       <button ng-click="items.push(items.length)">Add Item</button>
 *       <button ng-click="items = []">Reset Items</button>
 *       <pre>{{ items }}</pre>
 *       <test items="items"></test>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *      angular.module('do-check-module', [])
 *        .component('test', {
 *          bindings: { items: '<' },
 *          template:
 *            '<pre>{{ $ctrl.log | json }}</pre>',
 *          controller: function() {
 *            this.log = [];
 *
 *            this.$doCheck = function() {
 *              if (this.items_ref !== this.items) {
 *                this.log.push('doCheck: items changed');
 *                this.items_ref = this.items;
 *              }
 *              if (!angular.equals(this.items_clone, this.items)) {
 *                this.log.push('doCheck: items mutated');
 *                this.items_clone = angular.copy(this.items);
 *              }
 *            };
 *          }
 *        });
 *   </file>
 * </example>
 *
 *
 * ### Directive Definition Object
 *
 * The directive definition object provides instructions to the {@link ng.$compile
 * compiler}. The attributes are:
 *
 * #### `multiElement`
 * When this property is set to true (default is `false`), the HTML compiler will collect DOM nodes between
 * nodes with the attributes `directive-name-start` and `directive-name-end`, and group them
 * together as the directive elements. It is recommended that this feature be used on directives
 * which are not strictly behavioral (such as {@link ngClick}), and which
 * do not manipulate or replace child nodes (such as {@link ngInclude}).
 *
 * #### `priority`
 * When there are multiple directives defined on a single DOM element, sometimes it
 * is necessary to specify the order in which the directives are applied. The `priority` is used
 * to sort the directives before their `compile` functions get called. Priority is defined as a
 * number. Directives with greater numerical `priority` are compiled first. Pre-link functions
 * are also run in priority order, but post-link functions are run in reverse order. The order
 * of directives with the same priority is undefined. The default priority is `0`.
 *
 * #### `terminal`
 * If set to true then the current `priority` will be the last set of directives
 * which will execute (any directives at the current priority will still execute
 * as the order of execution on same `priority` is undefined). Note that expressions
 * and other directives used in the directive's template will also be excluded from execution.
 *
 * #### `scope`
 * The scope property can be `false`, `true`, or an object:
 *
 * * **`false` (default):** No scope will be created for the directive. The directive will use its
 * parent's scope.
 *
 * * **`true`:** A new child scope that prototypically inherits from its parent will be created for
 * the directive's element. If multiple directives on the same element request a new scope,
 * only one new scope is created.
 *
 * * **`{...}` (an object hash):** A new "isolate" scope is created for the directive's template.
 * The 'isolate' scope differs from normal scope in that it does not prototypically
 * inherit from its parent scope. This is useful when creating reusable components, which should not
 * accidentally read or modify data in the parent scope. Note that an isolate scope
 * directive without a `template` or `templateUrl` will not apply the isolate scope
 * to its children elements.
 *
 * The 'isolate' scope object hash defines a set of local scope properties derived from attributes on the
 * directive's element. These local properties are useful for aliasing values for templates. The keys in
 * the object hash map to the name of the property on the isolate scope; the values define how the property
 * is bound to the parent scope, via matching attributes on the directive's element:
 *
 * * `@` or `@attr` - bind a local scope property to the value of DOM attribute. The result is
 *   always a string since DOM attributes are strings. If no `attr` name is specified then the
 *   attribute name is assumed to be the same as the local name. Given `<my-component
 *   my-attr="hello {{name}}">` and the isolate scope definition `scope: { localName:'@myAttr' }`,
 *   the directive's scope property `localName` will reflect the interpolated value of `hello
 *   {{name}}`. As the `name` attribute changes so will the `localName` property on the directive's
 *   scope. The `name` is read from the parent scope (not the directive's scope).
 *
 * * `=` or `=attr` - set up a bidirectional binding between a local scope property and an expression
 *   passed via the attribute `attr`. The expression is evaluated in the context of the parent scope.
 *   If no `attr` name is specified then the attribute name is assumed to be the same as the local
 *   name. Given `<my-component my-attr="parentModel">` and the isolate scope definition `scope: {
 *   localModel: '=myAttr' }`, the property `localModel` on the directive's scope will reflect the
 *   value of `parentModel` on the parent scope. Changes to `parentModel` will be reflected in
 *   `localModel` and vice versa. Optional attributes should be marked as such with a question mark:
 *   `=?` or `=?attr`. If the binding expression is non-assignable, or if the attribute isn't
 *   optional and doesn't exist, an exception ({@link error/$compile/nonassign `$compile:nonassign`})
 *   will be thrown upon discovering changes to the local value, since it will be impossible to sync
 *   them back to the parent scope. By default, the {@link ng.$rootScope.Scope#$watch `$watch`}
 *   method is used for tracking changes, and the equality check is based on object identity.
 *   However, if an object literal or an array literal is passed as the binding expression, the
 *   equality check is done by value (using the {@link angular.equals} function). It's also possible
 *   to watch the evaluated value shallowly with {@link ng.$rootScope.Scope#$watchCollection
 *   `$watchCollection`}: use `=*` or `=*attr` (`=*?` or `=*?attr` if the attribute is optional).
 *
  * * `<` or `<attr` - set up a one-way (one-directional) binding between a local scope property and an
 *   expression passed via the attribute `attr`. The expression is evaluated in the context of the
 *   parent scope. If no `attr` name is specified then the attribute name is assumed to be the same as the
 *   local name. You can also make the binding optional by adding `?`: `<?` or `<?attr`.
 *
 *   For example, given `<my-component my-attr="parentModel">` and directive definition of
 *   `scope: { localModel:'<myAttr' }`, then the isolated scope property `localModel` will reflect the
 *   value of `parentModel` on the parent scope. Any changes to `parentModel` will be reflected
 *   in `localModel`, but changes in `localModel` will not reflect in `parentModel`. There are however
 *   two caveats:
 *     1. one-way binding does not copy the value from the parent to the isolate scope, it simply
 *     sets the same value. That means if your bound value is an object, changes to its properties
 *     in the isolated scope will be reflected in the parent scope (because both reference the same object).
 *     2. one-way binding watches changes to the **identity** of the parent value. That means the
 *     {@link ng.$rootScope.Scope#$watch `$watch`} on the parent value only fires if the reference
 *     to the value has changed. In most cases, this should not be of concern, but can be important
 *     to know if you one-way bind to an object, and then replace that object in the isolated scope.
 *     If you now change a property of the object in your parent scope, the change will not be
 *     propagated to the isolated scope, because the identity of the object on the parent scope
 *     has not changed. Instead you must assign a new object.
 *
 *   One-way binding is useful if you do not plan to propagate changes to your isolated scope bindings
 *   back to the parent. However, it does not make this completely impossible.
 *
 * * `&` or `&attr` - provides a way to execute an expression in the context of the parent scope. If
 *   no `attr` name is specified then the attribute name is assumed to be the same as the local name.
 *   Given `<my-component my-attr="count = count + value">` and the isolate scope definition `scope: {
 *   localFn:'&myAttr' }`, the isolate scope property `localFn` will point to a function wrapper for
 *   the `count = count + value` expression. Often it's desirable to pass data from the isolated scope
 *   via an expression to the parent scope. This can be done by passing a map of local variable names
 *   and values into the expression wrapper fn. For example, if the expression is `increment(amount)`
 *   then we can specify the amount value by calling the `localFn` as `localFn({amount: 22})`.
 *
 * In general it's possible to apply more than one directive to one element, but there might be limitations
 * depending on the type of scope required by the directives. The following points will help explain these limitations.
 * For simplicity only two directives are taken into account, but it is also applicable for several directives:
 *
 * * **no scope** + **no scope** => Two directives which don't require their own scope will use their parent's scope
 * * **child scope** + **no scope** =>  Both directives will share one single child scope
 * * **child scope** + **child scope** =>  Both directives will share one single child scope
 * * **isolated scope** + **no scope** =>  The isolated directive will use it's own created isolated scope. The other directive will use
 * its parent's scope
 * * **isolated scope** + **child scope** =>  **Won't work!** Only one scope can be related to one element. Therefore these directives cannot
 * be applied to the same element.
 * * **isolated scope** + **isolated scope**  =>  **Won't work!** Only one scope can be related to one element. Therefore these directives
 * cannot be applied to the same element.
 *
 *
 * #### `bindToController`
 * This property is used to bind scope properties directly to the controller. It can be either
 * `true` or an object hash with the same format as the `scope` property.
 *
 * When an isolate scope is used for a directive (see above), `bindToController: true` will
 * allow a component to have its properties bound to the controller, rather than to scope.
 *
 * After the controller is instantiated, the initial values of the isolate scope bindings will be bound to the controller
 * properties. You can access these bindings once they have been initialized by providing a controller method called
 * `$onInit`, which is called after all the controllers on an element have been constructed and had their bindings
 * initialized.
 *
 * <div class="alert alert-warning">
 * **Deprecation warning:** if `$compileProcvider.preAssignBindingsEnabled(true)` was called, bindings for non-ES6 class
 * controllers are bound to `this` before the controller constructor is called but this use is now deprecated. Please
 * place initialization code that relies upon bindings inside a `$onInit` method on the controller, instead.
 * </div>
 *
 * It is also possible to set `bindToController` to an object hash with the same format as the `scope` property.
 * This will set up the scope bindings to the controller directly. Note that `scope` can still be used
 * to define which kind of scope is created. By default, no scope is created. Use `scope: {}` to create an isolate
 * scope (useful for component directives).
 *
 * If both `bindToController` and `scope` are defined and have object hashes, `bindToController` overrides `scope`.
 *
 *
 * #### `controller`
 * Controller constructor function. The controller is instantiated before the
 * pre-linking phase and can be accessed by other directives (see
 * `require` attribute). This allows the directives to communicate with each other and augment
 * each other's behavior. The controller is injectable (and supports bracket notation) with the following locals:
 *
 * * `$scope` - Current scope associated with the element
 * * `$element` - Current element
 * * `$attrs` - Current attributes object for the element
 * * `$transclude` - A transclude linking function pre-bound to the correct transclusion scope:
 *   `function([scope], cloneLinkingFn, futureParentElement, slotName)`:
 *    * `scope`: (optional) override the scope.
 *    * `cloneLinkingFn`: (optional) argument to create clones of the original transcluded content.
 *    * `futureParentElement` (optional):
 *        * defines the parent to which the `cloneLinkingFn` will add the cloned elements.
 *        * default: `$element.parent()` resp. `$element` for `transclude:'element'` resp. `transclude:true`.
 *        * only needed for transcludes that are allowed to contain non html elements (e.g. SVG elements)
 *          and when the `cloneLinkingFn` is passed,
 *          as those elements need to created and cloned in a special way when they are defined outside their
 *          usual containers (e.g. like `<svg>`).
 *        * See also the `directive.templateNamespace` property.
 *    * `slotName`: (optional) the name of the slot to transclude. If falsy (e.g. `null`, `undefined` or `''`)
 *      then the default transclusion is provided.
 *    The `$transclude` function also has a method on it, `$transclude.isSlotFilled(slotName)`, which returns
 *    `true` if the specified slot contains content (i.e. one or more DOM nodes).
 *
 * #### `require`
 * Require another directive and inject its controller as the fourth argument to the linking function. The
 * `require` property can be a string, an array or an object:
 * * a **string** containing the name of the directive to pass to the linking function
 * * an **array** containing the names of directives to pass to the linking function. The argument passed to the
 * linking function will be an array of controllers in the same order as the names in the `require` property
 * * an **object** whose property values are the names of the directives to pass to the linking function. The argument
 * passed to the linking function will also be an object with matching keys, whose values will hold the corresponding
 * controllers.
 *
 * If the `require` property is an object and `bindToController` is truthy, then the required controllers are
 * bound to the controller using the keys of the `require` property. This binding occurs after all the controllers
 * have been constructed but before `$onInit` is called.
 * If the name of the required controller is the same as the local name (the key), the name can be
 * omitted. For example, `{parentDir: '^^'}` is equivalent to `{parentDir: '^^parentDir'}`.
 * See the {@link $compileProvider#component} helper for an example of how this can be used.
 * If no such required directive(s) can be found, or if the directive does not have a controller, then an error is
 * raised (unless no link function is specified and the required controllers are not being bound to the directive
 * controller, in which case error checking is skipped). The name can be prefixed with:
 *
 * * (no prefix) - Locate the required controller on the current element. Throw an error if not found.
 * * `?` - Attempt to locate the required controller or pass `null` to the `link` fn if not found.
 * * `^` - Locate the required controller by searching the element and its parents. Throw an error if not found.
 * * `^^` - Locate the required controller by searching the element's parents. Throw an error if not found.
 * * `?^` - Attempt to locate the required controller by searching the element and its parents or pass
 *   `null` to the `link` fn if not found.
 * * `?^^` - Attempt to locate the required controller by searching the element's parents, or pass
 *   `null` to the `link` fn if not found.
 *
 *
 * #### `controllerAs`
 * Identifier name for a reference to the controller in the directive's scope.
 * This allows the controller to be referenced from the directive template. This is especially
 * useful when a directive is used as component, i.e. with an `isolate` scope. It's also possible
 * to use it in a directive without an `isolate` / `new` scope, but you need to be aware that the
 * `controllerAs` reference might overwrite a property that already exists on the parent scope.
 *
 *
 * #### `restrict`
 * String of subset of `EACM` which restricts the directive to a specific directive
 * declaration style. If omitted, the defaults (elements and attributes) are used.
 *
 * * `E` - Element name (default): `<my-directive></my-directive>`
 * * `A` - Attribute (default): `<div my-directive="exp"></div>`
 * * `C` - Class: `<div class="my-directive: exp;"></div>`
 * * `M` - Comment: `<!-- directive: my-directive exp -->`
 *
 *
 * #### `templateNamespace`
 * String representing the document type used by the markup in the template.
 * AngularJS needs this information as those elements need to be created and cloned
 * in a special way when they are defined outside their usual containers like `<svg>` and `<math>`.
 *
 * * `html` - All root nodes in the template are HTML. Root nodes may also be
 *   top-level elements such as `<svg>` or `<math>`.
 * * `svg` - The root nodes in the template are SVG elements (excluding `<math>`).
 * * `math` - The root nodes in the template are MathML elements (excluding `<svg>`).
 *
 * If no `templateNamespace` is specified, then the namespace is considered to be `html`.
 *
 * #### `template`
 * HTML markup that may:
 * * Replace the contents of the directive's element (default).
 * * Replace the directive's element itself (if `replace` is true - DEPRECATED).
 * * Wrap the contents of the directive's element (if `transclude` is true).
 *
 * Value may be:
 *
 * * A string. For example `<div red-on-hover>{{delete_str}}</div>`.
 * * A function which takes two arguments `tElement` and `tAttrs` (described in the `compile`
 *   function api below) and returns a string value.
 *
 *
 * #### `templateUrl`
 * This is similar to `template` but the template is loaded from the specified URL, asynchronously.
 *
 * Because template loading is asynchronous the compiler will suspend compilation of directives on that element
 * for later when the template has been resolved.  In the meantime it will continue to compile and link
 * sibling and parent elements as though this element had not contained any directives.
 *
 * The compiler does not suspend the entire compilation to wait for templates to be loaded because this
 * would result in the whole app "stalling" until all templates are loaded asynchronously - even in the
 * case when only one deeply nested directive has `templateUrl`.
 *
 * Template loading is asynchronous even if the template has been preloaded into the {@link $templateCache}
 *
 * You can specify `templateUrl` as a string representing the URL or as a function which takes two
 * arguments `tElement` and `tAttrs` (described in the `compile` function api below) and returns
 * a string value representing the url.  In either case, the template URL is passed through {@link
 * $sce#getTrustedResourceUrl $sce.getTrustedResourceUrl}.
 *
 *
 * #### `replace` ([*DEPRECATED*!], will be removed in next major release - i.e. v2.0)
 * specify what the template should replace. Defaults to `false`.
 *
 * * `true` - the template will replace the directive's element.
 * * `false` - the template will replace the contents of the directive's element.
 *
 * The replacement process migrates all of the attributes / classes from the old element to the new
 * one. See the {@link guide/directive#template-expanding-directive
 * Directives Guide} for an example.
 *
 * There are very few scenarios where element replacement is required for the application function,
 * the main one being reusable custom components that are used within SVG contexts
 * (because SVG doesn't work with custom elements in the DOM tree).
 *
 * #### `transclude`
 * Extract the contents of the element where the directive appears and make it available to the directive.
 * The contents are compiled and provided to the directive as a **transclusion function**. See the
 * {@link $compile#transclusion Transclusion} section below.
 *
 *
 * #### `compile`
 *
 * ```js
 *   function compile(tElement, tAttrs, transclude) { ... }
 * ```
 *
 * The compile function deals with transforming the template DOM. Since most directives do not do
 * template transformation, it is not used often. The compile function takes the following arguments:
 *
 *   * `tElement` - template element - The element where the directive has been declared. It is
 *     safe to do template transformation on the element and child elements only.
 *
 *   * `tAttrs` - template attributes - Normalized list of attributes declared on this element shared
 *     between all directive compile functions.
 *
 *   * `transclude` -  [*DEPRECATED*!] A transclude linking function: `function(scope, cloneLinkingFn)`
 *
 * <div class="alert alert-warning">
 * **Note:** The template instance and the link instance may be different objects if the template has
 * been cloned. For this reason it is **not** safe to do anything other than DOM transformations that
 * apply to all cloned DOM nodes within the compile function. Specifically, DOM listener registration
 * should be done in a linking function rather than in a compile function.
 * </div>

 * <div class="alert alert-warning">
 * **Note:** The compile function cannot handle directives that recursively use themselves in their
 * own templates or compile functions. Compiling these directives results in an infinite loop and
 * stack overflow errors.
 *
 * This can be avoided by manually using $compile in the postLink function to imperatively compile
 * a directive's template instead of relying on automatic template compilation via `template` or
 * `templateUrl` declaration or manual compilation inside the compile function.
 * </div>
 *
 * <div class="alert alert-danger">
 * **Note:** The `transclude` function that is passed to the compile function is deprecated, as it
 *   e.g. does not know about the right outer scope. Please use the transclude function that is passed
 *   to the link function instead.
 * </div>

 * A compile function can have a return value which can be either a function or an object.
 *
 * * returning a (post-link) function - is equivalent to registering the linking function via the
 *   `link` property of the config object when the compile function is empty.
 *
 * * returning an object with function(s) registered via `pre` and `post` properties - allows you to
 *   control when a linking function should be called during the linking phase. See info about
 *   pre-linking and post-linking functions below.
 *
 *
 * #### `link`
 * This property is used only if the `compile` property is not defined.
 *
 * ```js
 *   function link(scope, iElement, iAttrs, controller, transcludeFn) { ... }
 * ```
 *
 * The link function is responsible for registering DOM listeners as well as updating the DOM. It is
 * executed after the template has been cloned. This is where most of the directive logic will be
 * put.
 *
 *   * `scope` - {@link ng.$rootScope.Scope Scope} - The scope to be used by the
 *     directive for registering {@link ng.$rootScope.Scope#$watch watches}.
 *
 *   * `iElement` - instance element - The element where the directive is to be used. It is safe to
 *     manipulate the children of the element only in `postLink` function since the children have
 *     already been linked.
 *
 *   * `iAttrs` - instance attributes - Normalized list of attributes declared on this element shared
 *     between all directive linking functions.
 *
 *   * `controller` - the directive's required controller instance(s) - Instances are shared
 *     among all directives, which allows the directives to use the controllers as a communication
 *     channel. The exact value depends on the directive's `require` property:
 *       * no controller(s) required: the directive's own controller, or `undefined` if it doesn't have one
 *       * `string`: the controller instance
 *       * `array`: array of controller instances
 *
 *     If a required controller cannot be found, and it is optional, the instance is `null`,
 *     otherwise the {@link error:$compile:ctreq Missing Required Controller} error is thrown.
 *
 *     Note that you can also require the directive's own controller - it will be made available like
 *     any other controller.
 *
 *   * `transcludeFn` - A transclude linking function pre-bound to the correct transclusion scope.
 *     This is the same as the `$transclude` parameter of directive controllers,
 *     see {@link ng.$compile#-controller- the controller section for details}.
 *     `function([scope], cloneLinkingFn, futureParentElement)`.
 *
 * #### Pre-linking function
 *
 * Executed before the child elements are linked. Not safe to do DOM transformation since the
 * compiler linking function will fail to locate the correct elements for linking.
 *
 * #### Post-linking function
 *
 * Executed after the child elements are linked.
 *
 * Note that child elements that contain `templateUrl` directives will not have been compiled
 * and linked since they are waiting for their template to load asynchronously and their own
 * compilation and linking has been suspended until that occurs.
 *
 * It is safe to do DOM transformation in the post-linking function on elements that are not waiting
 * for their async templates to be resolved.
 *
 *
 * ### Transclusion
 *
 * Transclusion is the process of extracting a collection of DOM elements from one part of the DOM and
 * copying them to another part of the DOM, while maintaining their connection to the original AngularJS
 * scope from where they were taken.
 *
 * Transclusion is used (often with {@link ngTransclude}) to insert the
 * original contents of a directive's element into a specified place in the template of the directive.
 * The benefit of transclusion, over simply moving the DOM elements manually, is that the transcluded
 * content has access to the properties on the scope from which it was taken, even if the directive
 * has isolated scope.
 * See the {@link guide/directive#creating-a-directive-that-wraps-other-elements Directives Guide}.
 *
 * This makes it possible for the widget to have private state for its template, while the transcluded
 * content has access to its originating scope.
 *
 * <div class="alert alert-warning">
 * **Note:** When testing an element transclude directive you must not place the directive at the root of the
 * DOM fragment that is being compiled. See {@link guide/unit-testing#testing-transclusion-directives
 * Testing Transclusion Directives}.
 * </div>
 *
 * There are three kinds of transclusion depending upon whether you want to transclude just the contents of the
 * directive's element, the entire element or multiple parts of the element contents:
 *
 * * `true` - transclude the content (i.e. the child nodes) of the directive's element.
 * * `'element'` - transclude the whole of the directive's element including any directives on this
 *   element that defined at a lower priority than this directive. When used, the `template`
 *   property is ignored.
 * * **`{...}` (an object hash):** - map elements of the content onto transclusion "slots" in the template.
 *
 * **Mult-slot transclusion** is declared by providing an object for the `transclude` property.
 *
 * This object is a map where the keys are the name of the slot to fill and the value is an element selector
 * used to match the HTML to the slot. The element selector should be in normalized form (e.g. `myElement`)
 * and will match the standard element variants (e.g. `my-element`, `my:element`, `data-my-element`, etc).
 *
 * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
 *
 * If the element selector is prefixed with a `?` then that slot is optional.
 *
 * For example, the transclude object `{ slotA: '?myCustomElement' }` maps `<my-custom-element>` elements to
 * the `slotA` slot, which can be accessed via the `$transclude` function or via the {@link ngTransclude} directive.
 *
 * Slots that are not marked as optional (`?`) will trigger a compile time error if there are no matching elements
 * in the transclude content. If you wish to know if an optional slot was filled with content, then you can call
 * `$transclude.isSlotFilled(slotName)` on the transclude function passed to the directive's link function and
 * injectable into the directive's controller.
 *
 *
 * #### Transclusion Functions
 *
 * When a directive requests transclusion, the compiler extracts its contents and provides a **transclusion
 * function** to the directive's `link` function and `controller`. This transclusion function is a special
 * **linking function** that will return the compiled contents linked to a new transclusion scope.
 *
 * <div class="alert alert-info">
 * If you are just using {@link ngTransclude} then you don't need to worry about this function, since
 * ngTransclude will deal with it for us.
 * </div>
 *
 * If you want to manually control the insertion and removal of the transcluded content in your directive
 * then you must use this transclude function. When you call a transclude function it returns a a jqLite/JQuery
 * object that contains the compiled DOM, which is linked to the correct transclusion scope.
 *
 * When you call a transclusion function you can pass in a **clone attach function**. This function accepts
 * two parameters, `function(clone, scope) { ... }`, where the `clone` is a fresh compiled copy of your transcluded
 * content and the `scope` is the newly created transclusion scope, which the clone will be linked to.
 *
 * <div class="alert alert-info">
 * **Best Practice**: Always provide a `cloneFn` (clone attach function) when you call a transclude function
 * since you then get a fresh clone of the original DOM and also have access to the new transclusion scope.
 * </div>
 *
 * It is normal practice to attach your transcluded content (`clone`) to the DOM inside your **clone
 * attach function**:
 *
 * ```js
 * var transcludedContent, transclusionScope;
 *
 * $transclude(function(clone, scope) {
 *   element.append(clone);
 *   transcludedContent = clone;
 *   transclusionScope = scope;
 * });
 * ```
 *
 * Later, if you want to remove the transcluded content from your DOM then you should also destroy the
 * associated transclusion scope:
 *
 * ```js
 * transcludedContent.remove();
 * transclusionScope.$destroy();
 * ```
 *
 * <div class="alert alert-info">
 * **Best Practice**: if you intend to add and remove transcluded content manually in your directive
 * (by calling the transclude function to get the DOM and calling `element.remove()` to remove it),
 * then you are also responsible for calling `$destroy` on the transclusion scope.
 * </div>
 *
 * The built-in DOM manipulation directives, such as {@link ngIf}, {@link ngSwitch} and {@link ngRepeat}
 * automatically destroy their transcluded clones as necessary so you do not need to worry about this if
 * you are simply using {@link ngTransclude} to inject the transclusion into your directive.
 *
 *
 * #### Transclusion Scopes
 *
 * When you call a transclude function it returns a DOM fragment that is pre-bound to a **transclusion
 * scope**. This scope is special, in that it is a child of the directive's scope (and so gets destroyed
 * when the directive's scope gets destroyed) but it inherits the properties of the scope from which it
 * was taken.
 *
 * For example consider a directive that uses transclusion and isolated scope. The DOM hierarchy might look
 * like this:
 *
 * ```html
 * <div ng-app>
 *   <div isolate>
 *     <div transclusion>
 *     </div>
 *   </div>
 * </div>
 * ```
 *
 * The `$parent` scope hierarchy will look like this:
 *
   ```
   - $rootScope
     - isolate
       - transclusion
   ```
 *
 * but the scopes will inherit prototypically from different scopes to their `$parent`.
 *
   ```
   - $rootScope
     - transclusion
   - isolate
   ```
 *
 *
 * ### Attributes
 *
 * The {@link ng.$compile.directive.Attributes Attributes} object - passed as a parameter in the
 * `link()` or `compile()` functions. It has a variety of uses.
 *
 * * *Accessing normalized attribute names:* Directives like 'ngBind' can be expressed in many ways:
 *   'ng:bind', `data-ng-bind`, or 'x-ng-bind'. The attributes object allows for normalized access
 *   to the attributes.
 *
 * * *Directive inter-communication:* All directives share the same instance of the attributes
 *   object which allows the directives to use the attributes object as inter directive
 *   communication.
 *
 * * *Supports interpolation:* Interpolation attributes are assigned to the attribute object
 *   allowing other directives to read the interpolated value.
 *
 * * *Observing interpolated attributes:* Use `$observe` to observe the value changes of attributes
 *   that contain interpolation (e.g. `src="{{bar}}"`). Not only is this very efficient but it's also
 *   the only way to easily get the actual value because during the linking phase the interpolation
 *   hasn't been evaluated yet and so the value is at this time set to `undefined`.
 *
 * ```js
 * function linkingFn(scope, elm, attrs, ctrl) {
 *   // get the attribute value
 *   console.log(attrs.ngModel);
 *
 *   // change the attribute
 *   attrs.$set('ngModel', 'new value');
 *
 *   // observe changes to interpolated attribute
 *   attrs.$observe('ngModel', function(value) {
 *     console.log('ngModel has changed value to ' + value);
 *   });
 * }
 * ```
 *
 * ## Example
 *
 * <div class="alert alert-warning">
 * **Note**: Typically directives are registered with `module.directive`. The example below is
 * to illustrate how `$compile` works.
 * </div>
 *
 <example module="compileExample" name="compile">
   <file name="index.html">
    <script>
      angular.module('compileExample', [], function($compileProvider) {
        // configure new 'compile' directive by passing a directive
        // factory function. The factory function injects the '$compile'
        $compileProvider.directive('compile', function($compile) {
          // directive factory creates a link function
          return function(scope, element, attrs) {
            scope.$watch(
              function(scope) {
                 // watch the 'compile' expression for changes
                return scope.$eval(attrs.compile);
              },
              function(value) {
                // when the 'compile' expression changes
                // assign it into the current DOM
                element.html(value);

                // compile the new DOM and link it to the current
                // scope.
                // NOTE: we only compile .childNodes so that
                // we don't get into infinite loop compiling ourselves
                $compile(element.contents())(scope);
              }
            );
          };
        });
      })
      .controller('GreeterController', ['$scope', function($scope) {
        $scope.name = 'Angular';
        $scope.html = 'Hello {{name}}';
      }]);
    </script>
    <div ng-controller="GreeterController">
      <input ng-model="name"> <br/>
      <textarea ng-model="html"></textarea> <br/>
      <div compile="html"></div>
    </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should auto compile', function() {
       var textarea = $('textarea');
       var output = $('div[compile]');
       // The initial state reads 'Hello Angular'.
       expect(output.getText()).toBe('Hello Angular');
       textarea.clear();
       textarea.sendKeys('{{name}}!');
       expect(output.getText()).toBe('Angular!');
     });
   </file>
 </example>

 *
 *
 * @param {string|DOMElement} element Element or HTML string to compile into a template function.
 * @param {function(angular.Scope, cloneAttachFn=)} transclude function available to directives - DEPRECATED.
 *
 * <div class="alert alert-danger">
 * **Note:** Passing a `transclude` function to the $compile function is deprecated, as it
 *   e.g. will not use the right outer scope. Please pass the transclude function as a
 *   `parentBoundTranscludeFn` to the link function instead.
 * </div>
 *
 * @param {number} maxPriority only apply directives lower than given priority (Only effects the
 *                 root element(s), not their children)
 * @returns {function(scope, cloneAttachFn=, options=)} a link function which is used to bind template
 * (a DOM element/tree) to a scope. Where:
 *
 *  * `scope` - A {@link ng.$rootScope.Scope Scope} to bind to.
 *  * `cloneAttachFn` - If `cloneAttachFn` is provided, then the link function will clone the
 *  `template` and call the `cloneAttachFn` function allowing the caller to attach the
 *  cloned elements to the DOM document at the appropriate place. The `cloneAttachFn` is
 *  called as: <br/> `cloneAttachFn(clonedElement, scope)` where:
 *
 *      * `clonedElement` - is a clone of the original `element` passed into the compiler.
 *      * `scope` - is the current scope with which the linking function is working with.
 *
 *  * `options` - An optional object hash with linking options. If `options` is provided, then the following
 *  keys may be used to control linking behavior:
 *
 *      * `parentBoundTranscludeFn` - the transclude function made available to
 *        directives; if given, it will be passed through to the link functions of
 *        directives found in `element` during compilation.
 *      * `transcludeControllers` - an object hash with keys that map controller names
 *        to a hash with the key `instance`, which maps to the controller instance;
 *        if given, it will make the controllers available to directives on the compileNode:
 *        ```
 *        {
 *          parent: {
 *            instance: parentControllerInstance
 *          }
 *        }
 *        ```
 *      * `futureParentElement` - defines the parent to which the `cloneAttachFn` will add
 *        the cloned elements; only needed for transcludes that are allowed to contain non html
 *        elements (e.g. SVG elements). See also the directive.controller property.
 *
 * Calling the linking function returns the element of the template. It is either the original
 * element passed in, or the clone of the element if the `cloneAttachFn` is provided.
 *
 * After linking the view is not updated until after a call to $digest which typically is done by
 * Angular automatically.
 *
 * If you need access to the bound view, there are two ways to do it:
 *
 * - If you are not asking the linking function to clone the template, create the DOM element(s)
 *   before you send them to the compiler and keep this reference around.
 *   ```js
 *     var element = $compile('<p>{{total}}</p>')(scope);
 *   ```
 *
 * - if on the other hand, you need the element to be cloned, the view reference from the original
 *   example would not point to the clone, but rather to the original template that was cloned. In
 *   this case, you can access the clone via the cloneAttachFn:
 *   ```js
 *     var templateElement = angular.element('<p>{{total}}</p>'),
 *         scope = ....;
 *
 *     var clonedElement = $compile(templateElement)(scope, function(clonedElement, scope) {
 *       //attach the clone to DOM document at the right place
 *     });
 *
 *     //now we have reference to the cloned DOM via `clonedElement`
 *   ```
 *
 *
 * For information on how the compiler works, see the
 * {@link guide/compiler Angular HTML Compiler} section of the Developer Guide.
 *
 * @knownIssue
 *
 * ### Double Compilation
 *
   Double compilation occurs when an already compiled part of the DOM gets
   compiled again. This is an undesired effect and can lead to misbehaving directives, performance issues,
   and memory leaks. Refer to the Compiler Guide {@link guide/compiler#double-compilation-and-how-to-avoid-it
   section on double compilation} for an in-depth explanation and ways to avoid it.
 *
 */

var $compileMinErr = minErr('$compile');

function UNINITIALIZED_VALUE() {}
var _UNINITIALIZED_VALUE = new UNINITIALIZED_VALUE();

/**
 * @ngdoc provider
 * @name $compileProvider
 *
 * @description
 */
$CompileProvider.$inject = ['$provide', '$$sanitizeUriProvider'];
/** @this */
function $CompileProvider($provide, $$sanitizeUriProvider) {
  var hasDirectives = {},
      Suffix = 'Directive',
      COMMENT_DIRECTIVE_REGEXP = /^\s*directive:\s*([\w-]+)\s+(.*)$/,
      CLASS_DIRECTIVE_REGEXP = /(([\w-]+)(?::([^;]+))?;?)/,
      ALL_OR_NOTHING_ATTRS = makeMap('ngSrc,ngSrcset,src,srcset'),
      REQUIRE_PREFIX_REGEXP = /^(?:(\^\^?)?(\?)?(\^\^?)?)?/;

  // Ref: http://developers.whatwg.org/webappapis.html#event-handler-idl-attributes
  // The assumption is that future DOM event attribute names will begin with
  // 'on' and be composed of only English letters.
  var EVENT_HANDLER_ATTR_REGEXP = /^(on[a-z]+|formaction)$/;
  var bindingCache = createMap();

  function parseIsolateBindings(scope, directiveName, isController) {
    var LOCAL_REGEXP = /^\s*([@&<]|=(\*?))(\??)\s*([\w$]*)\s*$/;

    var bindings = createMap();

    forEach(scope, function(definition, scopeName) {
      if (definition in bindingCache) {
        bindings[scopeName] = bindingCache[definition];
        return;
      }
      var match = definition.match(LOCAL_REGEXP);

      if (!match) {
        throw $compileMinErr('iscp',
            'Invalid {3} for directive \'{0}\'.' +
            ' Definition: {... {1}: \'{2}\' ...}',
            directiveName, scopeName, definition,
            (isController ? 'controller bindings definition' :
            'isolate scope definition'));
      }

      bindings[scopeName] = {
        mode: match[1][0],
        collection: match[2] === '*',
        optional: match[3] === '?',
        attrName: match[4] || scopeName
      };
      if (match[4]) {
        bindingCache[definition] = bindings[scopeName];
      }
    });

    return bindings;
  }

  function parseDirectiveBindings(directive, directiveName) {
    var bindings = {
      isolateScope: null,
      bindToController: null
    };
    if (isObject(directive.scope)) {
      if (directive.bindToController === true) {
        bindings.bindToController = parseIsolateBindings(directive.scope,
                                                         directiveName, true);
        bindings.isolateScope = {};
      } else {
        bindings.isolateScope = parseIsolateBindings(directive.scope,
                                                     directiveName, false);
      }
    }
    if (isObject(directive.bindToController)) {
      bindings.bindToController =
          parseIsolateBindings(directive.bindToController, directiveName, true);
    }
    if (bindings.bindToController && !directive.controller) {
      // There is no controller
      throw $compileMinErr('noctrl',
            'Cannot bind to controller without directive \'{0}\'s controller.',
            directiveName);
    }
    return bindings;
  }

  function assertValidDirectiveName(name) {
    var letter = name.charAt(0);
    if (!letter || letter !== lowercase(letter)) {
      throw $compileMinErr('baddir', 'Directive/Component name \'{0}\' is invalid. The first character must be a lowercase letter', name);
    }
    if (name !== name.trim()) {
      throw $compileMinErr('baddir',
            'Directive/Component name \'{0}\' is invalid. The name should not contain leading or trailing whitespaces',
            name);
    }
  }

  function getDirectiveRequire(directive) {
    var require = directive.require || (directive.controller && directive.name);

    if (!isArray(require) && isObject(require)) {
      forEach(require, function(value, key) {
        var match = value.match(REQUIRE_PREFIX_REGEXP);
        var name = value.substring(match[0].length);
        if (!name) require[key] = match[0] + key;
      });
    }

    return require;
  }

  function getDirectiveRestrict(restrict, name) {
    if (restrict && !(isString(restrict) && /[EACM]/.test(restrict))) {
      throw $compileMinErr('badrestrict',
          'Restrict property \'{0}\' of directive \'{1}\' is invalid',
          restrict,
          name);
    }

    return restrict || 'EA';
  }

  /**
   * @ngdoc method
   * @name $compileProvider#directive
   * @kind function
   *
   * @description
   * Register a new directive with the compiler.
   *
   * @param {string|Object} name Name of the directive in camel-case (i.e. <code>ngBind</code> which
   *    will match as <code>ng-bind</code>), or an object map of directives where the keys are the
   *    names and the values are the factories.
   * @param {Function|Array} directiveFactory An injectable directive factory function. See the
   *    {@link guide/directive directive guide} and the {@link $compile compile API} for more info.
   * @returns {ng.$compileProvider} Self for chaining.
   */
  this.directive = function registerDirective(name, directiveFactory) {
    assertArg(name, 'name');
    assertNotHasOwnProperty(name, 'directive');
    if (isString(name)) {
      assertValidDirectiveName(name);
      assertArg(directiveFactory, 'directiveFactory');
      if (!hasDirectives.hasOwnProperty(name)) {
        hasDirectives[name] = [];
        $provide.factory(name + Suffix, ['$injector', '$exceptionHandler',
          function($injector, $exceptionHandler) {
            var directives = [];
            forEach(hasDirectives[name], function(directiveFactory, index) {
              try {
                var directive = $injector.invoke(directiveFactory);
                if (isFunction(directive)) {
                  directive = { compile: valueFn(directive) };
                } else if (!directive.compile && directive.link) {
                  directive.compile = valueFn(directive.link);
                }
                directive.priority = directive.priority || 0;
                directive.index = index;
                directive.name = directive.name || name;
                directive.require = getDirectiveRequire(directive);
                directive.restrict = getDirectiveRestrict(directive.restrict, name);
                directive.$$moduleName = directiveFactory.$$moduleName;
                directives.push(directive);
              } catch (e) {
                $exceptionHandler(e);
              }
            });
            return directives;
          }]);
      }
      hasDirectives[name].push(directiveFactory);
    } else {
      forEach(name, reverseParams(registerDirective));
    }
    return this;
  };

  /**
   * @ngdoc method
   * @name $compileProvider#component
   * @module ng
   * @param {string|Object} name Name of the component in camelCase (i.e. `myComp` which will match `<my-comp>`),
   *    or an object map of components where the keys are the names and the values are the component definition objects.
   * @param {Object} options Component definition object (a simplified
   *    {@link ng.$compile#directive-definition-object directive definition object}),
   *    with the following properties (all optional):
   *
   *    - `controller` – `{(string|function()=}` – controller constructor function that should be
   *      associated with newly created scope or the name of a {@link ng.$compile#-controller-
   *      registered controller} if passed as a string. An empty `noop` function by default.
   *    - `controllerAs` – `{string=}` – identifier name for to reference the controller in the component's scope.
   *      If present, the controller will be published to scope under the `controllerAs` name.
   *      If not present, this will default to be `$ctrl`.
   *    - `template` – `{string=|function()=}` – html template as a string or a function that
   *      returns an html template as a string which should be used as the contents of this component.
   *      Empty string by default.
   *
   *      If `template` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `templateUrl` – `{string=|function()=}` – path or function that returns a path to an html
   *      template that should be used  as the contents of this component.
   *
   *      If `templateUrl` is a function, then it is {@link auto.$injector#invoke injected} with
   *      the following locals:
   *
   *      - `$element` - Current element
   *      - `$attrs` - Current attributes object for the element
   *
   *    - `bindings` – `{object=}` – defines bindings between DOM attributes and component properties.
   *      Component properties are always bound to the component controller and not to the scope.
   *      See {@link ng.$compile#-bindtocontroller- `bindToController`}.
   *    - `transclude` – `{boolean=}` – whether {@link $compile#transclusion content transclusion} is enabled.
   *      Disabled by default.
   *    - `require` - `{Object<string, string>=}` - requires the controllers of other directives and binds them to
   *      this component's controller. The object keys specify the property names under which the required
   *      controllers (object values) will be bound. See {@link ng.$compile#-require- `require`}.
   *    - `$...` – additional properties to attach to the directive factory function and the controller
   *      constructor function. (This is used by the component router to annotate)
   *
   * @returns {ng.$compileProvider} the compile provider itself, for chaining of function calls.
   * @description
   * Register a **component definition** with the compiler. This is a shorthand for registering a special
   * type of directive, which represents a self-contained UI component in your application. Such components
   * are always isolated (i.e. `scope: {}`) and are always restricted to elements (i.e. `restrict: 'E'`).
   *
   * Component definitions are very simple and do not require as much configuration as defining general
   * directives. Component definitions usually consist only of a template and a controller backing it.
   *
   * In order to make the definition easier, components enforce best practices like use of `controllerAs`,
   * `bindToController`. They always have **isolate scope** and are restricted to elements.
   *
   * Here are a few examples of how you would usually define components:
   *
   * ```js
   *   var myMod = angular.module(...);
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     controller: function() {
   *       this.name = 'shahar';
   *     }
   *   });
   *
   *   myMod.component('myComp', {
   *     template: '<div>My name is {{$ctrl.name}}</div>',
   *     bindings: {name: '@'}
   *   });
   *
   *   myMod.component('myComp', {
   *     templateUrl: 'views/my-comp.html',
   *     controller: 'MyCtrl',
   *     controllerAs: 'ctrl',
   *     bindings: {name: '@'}
   *   });
   *
   * ```
   * For more examples, and an in-depth guide, see the {@link guide/component component guide}.
   *
   * <br />
   * See also {@link ng.$compileProvider#directive $compileProvider.directive()}.
   */
  this.component = function registerComponent(name, options) {
    if (!isString(name)) {
      forEach(name, reverseParams(bind(this, registerComponent)));
      return this;
    }

    var controller = options.controller || function() {};

    function factory($injector) {
      function makeInjectable(fn) {
        if (isFunction(fn) || isArray(fn)) {
          return /** @this */ function(tElement, tAttrs) {
            return $injector.invoke(fn, this, {$element: tElement, $attrs: tAttrs});
          };
        } else {
          return fn;
        }
      }

      var template = (!options.template && !options.templateUrl ? '' : options.template);
      var ddo = {
        controller: controller,
        controllerAs: identifierForController(options.controller) || options.controllerAs || '$ctrl',
        template: makeInjectable(template),
        templateUrl: makeInjectable(options.templateUrl),
        transclude: options.transclude,
        scope: {},
        bindToController: options.bindings || {},
        restrict: 'E',
        require: options.require
      };

      // Copy annotations (starting with $) over to the DDO
      forEach(options, function(val, key) {
        if (key.charAt(0) === '$') ddo[key] = val;
      });

      return ddo;
    }

    // TODO(pete) remove the following `forEach` before we release 1.6.0
    // The component-router@0.2.0 looks for the annotations on the controller constructor
    // Nothing in Angular looks for annotations on the factory function but we can't remove
    // it from 1.5.x yet.

    // Copy any annotation properties (starting with $) over to the factory and controller constructor functions
    // These could be used by libraries such as the new component router
    forEach(options, function(val, key) {
      if (key.charAt(0) === '$') {
        factory[key] = val;
        // Don't try to copy over annotations to named controller
        if (isFunction(controller)) controller[key] = val;
      }
    });

    factory.$inject = ['$injector'];

    return this.directive(name, factory);
  };


  /**
   * @ngdoc method
   * @name $compileProvider#aHrefSanitizationWhitelist
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at preventing XSS attacks via html links.
   *
   * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.aHrefSanitizationWhitelist(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.aHrefSanitizationWhitelist();
    }
  };


  /**
   * @ngdoc method
   * @name $compileProvider#imgSrcSanitizationWhitelist
   * @kind function
   *
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      $$sanitizeUriProvider.imgSrcSanitizationWhitelist(regexp);
      return this;
    } else {
      return $$sanitizeUriProvider.imgSrcSanitizationWhitelist();
    }
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#debugInfoEnabled
   *
   * @param {boolean=} enabled update the debugInfoEnabled state if provided, otherwise just return the
   * current debugInfoEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable various debug runtime information in the compiler such as adding
   * binding information and a reference to the current scope on to DOM elements.
   * If enabled, the compiler will add the following to DOM elements that have been bound to the scope
   * * `ng-binding` CSS class
   * * `$binding` data property containing an array of the binding expressions
   *
   * You may want to disable this in production for a significant performance boost. See
   * {@link guide/production#disabling-debug-data Disabling Debug Data} for more.
   *
   * The default value is true.
   */
  var debugInfoEnabled = true;
  this.debugInfoEnabled = function(enabled) {
    if (isDefined(enabled)) {
      debugInfoEnabled = enabled;
      return this;
    }
    return debugInfoEnabled;
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#preAssignBindingsEnabled
   *
   * @param {boolean=} enabled update the preAssignBindingsEnabled state if provided, otherwise just return the
   * current preAssignBindingsEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable whether directive controllers are assigned bindings before
   * calling the controller's constructor.
   * If enabled (true), the compiler assigns the value of each of the bindings to the
   * properties of the controller object before the constructor of this object is called.
   *
   * If disabled (false), the compiler calls the constructor first before assigning bindings.
   *
   * The default value is false.
   *
   * @deprecated
   * sinceVersion="1.6.0"
   * removeVersion="1.7.0"
   *
   * This method and the option to assign the bindings before calling the controller's constructor
   * will be removed in v1.7.0.
   */
  var preAssignBindingsEnabled = false;
  this.preAssignBindingsEnabled = function(enabled) {
    if (isDefined(enabled)) {
      preAssignBindingsEnabled = enabled;
      return this;
    }
    return preAssignBindingsEnabled;
  };

  /**
   * @ngdoc method
   * @name  $compileProvider#strictComponentBindingsEnabled
   *
   * @param {boolean=} enabled update the strictComponentBindingsEnabled state if provided, otherwise just return the
   * current strictComponentBindingsEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @kind function
   *
   * @description
   * Call this method to enable/disable strict component bindings check. If enabled, the compiler will enforce that
   * for all bindings of a component that are not set as optional with `?`, an attribute needs to be provided
   * on the component's HTML tag.
   *
   * The default value is false.
   */
  var strictComponentBindingsEnabled = false;
  this.strictComponentBindingsEnabled = function(enabled) {
    if (isDefined(enabled)) {
      strictComponentBindingsEnabled = enabled;
      return this;
    }
    return strictComponentBindingsEnabled;
  };

  var TTL = 10;
  /**
   * @ngdoc method
   * @name $compileProvider#onChangesTtl
   * @description
   *
   * Sets the number of times `$onChanges` hooks can trigger new changes before giving up and
   * assuming that the model is unstable.
   *
   * The current default is 10 iterations.
   *
   * In complex applications it's possible that dependencies between `$onChanges` hooks and bindings will result
   * in several iterations of calls to these hooks. However if an application needs more than the default 10
   * iterations to stabilize then you should investigate what is causing the model to continuously change during
   * the `$onChanges` hook execution.
   *
   * Increasing the TTL could have performance implications, so you should not change it without proper justification.
   *
   * @param {number} limit The number of `$onChanges` hook iterations.
   * @returns {number|object} the current limit (or `this` if called as a setter for chaining)
   */
  this.onChangesTtl = function(value) {
    if (arguments.length) {
      TTL = value;
      return this;
    }
    return TTL;
  };

  var commentDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#commentDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on comments should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on comments for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check comments when looking for directives.
   * This should however only be used if you are sure that no comment directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on comments
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.commentDirectivesEnabled = function(value) {
    if (arguments.length) {
      commentDirectivesEnabledConfig = value;
      return this;
    }
    return commentDirectivesEnabledConfig;
  };


  var cssClassDirectivesEnabledConfig = true;
  /**
   * @ngdoc method
   * @name $compileProvider#cssClassDirectivesEnabled
   * @description
   *
   * It indicates to the compiler
   * whether or not directives on element classes should be compiled.
   * Defaults to `true`.
   *
   * Calling this function with false disables the compilation of directives
   * on element classes for the whole application.
   * This results in a compilation performance gain,
   * as the compiler doesn't have to check element classes when looking for directives.
   * This should however only be used if you are sure that no class directives are used in
   * the application (including any 3rd party directives).
   *
   * @param {boolean} enabled `false` if the compiler may ignore directives on element classes
   * @returns {boolean|object} the current value (or `this` if called as a setter for chaining)
   */
  this.cssClassDirectivesEnabled = function(value) {
    if (arguments.length) {
      cssClassDirectivesEnabledConfig = value;
      return this;
    }
    return cssClassDirectivesEnabledConfig;
  };

  this.$get = [
            '$injector', '$interpolate', '$exceptionHandler', '$templateRequest', '$parse',
            '$controller', '$rootScope', '$sce', '$animate', '$$sanitizeUri',
    function($injector,   $interpolate,   $exceptionHandler,   $templateRequest,   $parse,
             $controller,   $rootScope,   $sce,   $animate,   $$sanitizeUri) {

    var SIMPLE_ATTR_NAME = /^\w/;
    var specialAttrHolder = window.document.createElement('div');


    var commentDirectivesEnabled = commentDirectivesEnabledConfig;
    var cssClassDirectivesEnabled = cssClassDirectivesEnabledConfig;


    var onChangesTtl = TTL;
    // The onChanges hooks should all be run together in a single digest
    // When changes occur, the call to trigger their hooks will be added to this queue
    var onChangesQueue;

    // This function is called in a $$postDigest to trigger all the onChanges hooks in a single digest
    function flushOnChangesQueue() {
      try {
        if (!(--onChangesTtl)) {
          // We have hit the TTL limit so reset everything
          onChangesQueue = undefined;
          throw $compileMinErr('infchng', '{0} $onChanges() iterations reached. Aborting!\n', TTL);
        }
        // We must run this hook in an apply since the $$postDigest runs outside apply
        $rootScope.$apply(function() {
          var errors = [];
          for (var i = 0, ii = onChangesQueue.length; i < ii; ++i) {
            try {
              onChangesQueue[i]();
            } catch (e) {
              errors.push(e);
            }
          }
          // Reset the queue to trigger a new schedule next time there is a change
          onChangesQueue = undefined;
          if (errors.length) {
            throw errors;
          }
        });
      } finally {
        onChangesTtl++;
      }
    }


    function Attributes(element, attributesToCopy) {
      if (attributesToCopy) {
        var keys = Object.keys(attributesToCopy);
        var i, l, key;

        for (i = 0, l = keys.length; i < l; i++) {
          key = keys[i];
          this[key] = attributesToCopy[key];
        }
      } else {
        this.$attr = {};
      }

      this.$$element = element;
    }

    Attributes.prototype = {
      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$normalize
       * @kind function
       *
       * @description
       * Converts an attribute name (e.g. dash/colon/underscore-delimited string, optionally prefixed with `x-` or
       * `data-`) to its normalized, camelCase form.
       *
       * Also there is special case for Moz prefix starting with upper case letter.
       *
       * For further information check out the guide on {@link guide/directive#matching-directives Matching Directives}
       *
       * @param {string} name Name to normalize
       */
      $normalize: directiveNormalize,


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$addClass
       * @kind function
       *
       * @description
       * Adds the CSS class value specified by the classVal parameter to the element. If animations
       * are enabled then an animation will be triggered for the class addition.
       *
       * @param {string} classVal The className value that will be added to the element
       */
      $addClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.addClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$removeClass
       * @kind function
       *
       * @description
       * Removes the CSS class value specified by the classVal parameter from the element. If
       * animations are enabled then an animation will be triggered for the class removal.
       *
       * @param {string} classVal The className value that will be removed from the element
       */
      $removeClass: function(classVal) {
        if (classVal && classVal.length > 0) {
          $animate.removeClass(this.$$element, classVal);
        }
      },

      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$updateClass
       * @kind function
       *
       * @description
       * Adds and removes the appropriate CSS class values to the element based on the difference
       * between the new and old CSS class values (specified as newClasses and oldClasses).
       *
       * @param {string} newClasses The current CSS className value
       * @param {string} oldClasses The former CSS className value
       */
      $updateClass: function(newClasses, oldClasses) {
        var toAdd = tokenDifference(newClasses, oldClasses);
        if (toAdd && toAdd.length) {
          $animate.addClass(this.$$element, toAdd);
        }

        var toRemove = tokenDifference(oldClasses, newClasses);
        if (toRemove && toRemove.length) {
          $animate.removeClass(this.$$element, toRemove);
        }
      },

      /**
       * Set a normalized attribute on the element in a way such that all directives
       * can share the attribute. This function properly handles boolean attributes.
       * @param {string} key Normalized key. (ie ngAttribute)
       * @param {string|boolean} value The value to set. If `null` attribute will be deleted.
       * @param {boolean=} writeAttr If false, does not write the value to DOM element attribute.
       *     Defaults to true.
       * @param {string=} attrName Optional none normalized name. Defaults to key.
       */
      $set: function(key, value, writeAttr, attrName) {
        // TODO: decide whether or not to throw an error if "class"
        //is set through this function since it may cause $updateClass to
        //become unstable.

        var node = this.$$element[0],
            booleanKey = getBooleanAttrName(node, key),
            aliasedKey = getAliasedAttrName(key),
            observer = key,
            nodeName;

        if (booleanKey) {
          this.$$element.prop(key, value);
          attrName = booleanKey;
        } else if (aliasedKey) {
          this[aliasedKey] = value;
          observer = aliasedKey;
        }

        this[key] = value;

        // translate normalized key to actual key
        if (attrName) {
          this.$attr[key] = attrName;
        } else {
          attrName = this.$attr[key];
          if (!attrName) {
            this.$attr[key] = attrName = snake_case(key, '-');
          }
        }

        nodeName = nodeName_(this.$$element);

        if ((nodeName === 'a' && (key === 'href' || key === 'xlinkHref')) ||
            (nodeName === 'img' && key === 'src')) {
          // sanitize a[href] and img[src] values
          this[key] = value = $$sanitizeUri(value, key === 'src');
        } else if (nodeName === 'img' && key === 'srcset' && isDefined(value)) {
          // sanitize img[srcset] values
          var result = '';

          // first check if there are spaces because it's not the same pattern
          var trimmedSrcset = trim(value);
          //                (   999x   ,|   999w   ,|   ,|,   )
          var srcPattern = /(\s+\d+x\s*,|\s+\d+w\s*,|\s+,|,\s+)/;
          var pattern = /\s/.test(trimmedSrcset) ? srcPattern : /(,)/;

          // split srcset into tuple of uri and descriptor except for the last item
          var rawUris = trimmedSrcset.split(pattern);

          // for each tuples
          var nbrUrisWith2parts = Math.floor(rawUris.length / 2);
          for (var i = 0; i < nbrUrisWith2parts; i++) {
            var innerIdx = i * 2;
            // sanitize the uri
            result += $$sanitizeUri(trim(rawUris[innerIdx]), true);
            // add the descriptor
            result += (' ' + trim(rawUris[innerIdx + 1]));
          }

          // split the last item into uri and descriptor
          var lastTuple = trim(rawUris[i * 2]).split(/\s/);

          // sanitize the last uri
          result += $$sanitizeUri(trim(lastTuple[0]), true);

          // and add the last descriptor if any
          if (lastTuple.length === 2) {
            result += (' ' + trim(lastTuple[1]));
          }
          this[key] = value = result;
        }

        if (writeAttr !== false) {
          if (value === null || isUndefined(value)) {
            this.$$element.removeAttr(attrName);
          } else {
            if (SIMPLE_ATTR_NAME.test(attrName)) {
              this.$$element.attr(attrName, value);
            } else {
              setSpecialAttr(this.$$element[0], attrName, value);
            }
          }
        }

        // fire observers
        var $$observers = this.$$observers;
        if ($$observers) {
          forEach($$observers[observer], function(fn) {
            try {
              fn(value);
            } catch (e) {
              $exceptionHandler(e);
            }
          });
        }
      },


      /**
       * @ngdoc method
       * @name $compile.directive.Attributes#$observe
       * @kind function
       *
       * @description
       * Observes an interpolated attribute.
       *
       * The observer function will be invoked once during the next `$digest` following
       * compilation. The observer is then invoked whenever the interpolated value
       * changes.
       *
       * @param {string} key Normalized key. (ie ngAttribute) .
       * @param {function(interpolatedValue)} fn Function that will be called whenever
                the interpolated value of the attribute changes.
       *        See the {@link guide/interpolation#how-text-and-attribute-bindings-work Interpolation
       *        guide} for more info.
       * @returns {function()} Returns a deregistration function for this observer.
       */
      $observe: function(key, fn) {
        var attrs = this,
            $$observers = (attrs.$$observers || (attrs.$$observers = createMap())),
            listeners = ($$observers[key] || ($$observers[key] = []));

        listeners.push(fn);
        $rootScope.$evalAsync(function() {
          if (!listeners.$$inter && attrs.hasOwnProperty(key) && !isUndefined(attrs[key])) {
            // no one registered attribute interpolation function, so lets call it manually
            fn(attrs[key]);
          }
        });

        return function() {
          arrayRemove(listeners, fn);
        };
      }
    };

    function setSpecialAttr(element, attrName, value) {
      // Attributes names that do not start with letters (such as `(click)`) cannot be set using `setAttribute`
      // so we have to jump through some hoops to get such an attribute
      // https://github.com/angular/angular.js/pull/13318
      specialAttrHolder.innerHTML = '<span ' + attrName + '>';
      var attributes = specialAttrHolder.firstChild.attributes;
      var attribute = attributes[0];
      // We have to remove the attribute from its container element before we can add it to the destination element
      attributes.removeNamedItem(attribute.name);
      attribute.value = value;
      element.attributes.setNamedItem(attribute);
    }

    function safeAddClass($element, className) {
      try {
        $element.addClass(className);
      } catch (e) {
        // ignore, since it means that we are trying to set class on
        // SVG element, where class name is read-only.
      }
    }


    var startSymbol = $interpolate.startSymbol(),
        endSymbol = $interpolate.endSymbol(),
        denormalizeTemplate = (startSymbol === '{{' && endSymbol  === '}}')
            ? identity
            : function denormalizeTemplate(template) {
              return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
        },
        NG_ATTR_BINDING = /^ngAttr[A-Z]/;
    var MULTI_ELEMENT_DIR_RE = /^(.+)Start$/;

    compile.$$addBindingInfo = debugInfoEnabled ? function $$addBindingInfo($element, binding) {
      var bindings = $element.data('$binding') || [];

      if (isArray(binding)) {
        bindings = bindings.concat(binding);
      } else {
        bindings.push(binding);
      }

      $element.data('$binding', bindings);
    } : noop;

    compile.$$addBindingClass = debugInfoEnabled ? function $$addBindingClass($element) {
      safeAddClass($element, 'ng-binding');
    } : noop;

    compile.$$addScopeInfo = debugInfoEnabled ? function $$addScopeInfo($element, scope, isolated, noTemplate) {
      var dataName = isolated ? (noTemplate ? '$isolateScopeNoTemplate' : '$isolateScope') : '$scope';
      $element.data(dataName, scope);
    } : noop;

    compile.$$addScopeClass = debugInfoEnabled ? function $$addScopeClass($element, isolated) {
      safeAddClass($element, isolated ? 'ng-isolate-scope' : 'ng-scope');
    } : noop;

    compile.$$createComment = function(directiveName, comment) {
      var content = '';
      if (debugInfoEnabled) {
        content = ' ' + (directiveName || '') + ': ';
        if (comment) content += comment + ' ';
      }
      return window.document.createComment(content);
    };

    return compile;

    //================================

    function compile($compileNodes, transcludeFn, maxPriority, ignoreDirective,
                        previousCompileContext) {
      if (!($compileNodes instanceof jqLite)) {
        // jquery always rewraps, whereas we need to preserve the original selector so that we can
        // modify it.
        $compileNodes = jqLite($compileNodes);
      }
      var compositeLinkFn =
              compileNodes($compileNodes, transcludeFn, $compileNodes,
                           maxPriority, ignoreDirective, previousCompileContext);
      compile.$$addScopeClass($compileNodes);
      var namespace = null;
      return function publicLinkFn(scope, cloneConnectFn, options) {
        if (!$compileNodes) {
          throw $compileMinErr('multilink', 'This element has already been linked.');
        }
        assertArg(scope, 'scope');

        if (previousCompileContext && previousCompileContext.needsNewScope) {
          // A parent directive did a replace and a directive on this element asked
          // for transclusion, which caused us to lose a layer of element on which
          // we could hold the new transclusion scope, so we will create it manually
          // here.
          scope = scope.$parent.$new();
        }

        options = options || {};
        var parentBoundTranscludeFn = options.parentBoundTranscludeFn,
          transcludeControllers = options.transcludeControllers,
          futureParentElement = options.futureParentElement;

        // When `parentBoundTranscludeFn` is passed, it is a
        // `controllersBoundTransclude` function (it was previously passed
        // as `transclude` to directive.link) so we must unwrap it to get
        // its `boundTranscludeFn`
        if (parentBoundTranscludeFn && parentBoundTranscludeFn.$$boundTransclude) {
          parentBoundTranscludeFn = parentBoundTranscludeFn.$$boundTransclude;
        }

        if (!namespace) {
          namespace = detectNamespaceForChildElements(futureParentElement);
        }
        var $linkNode;
        if (namespace !== 'html') {
          // When using a directive with replace:true and templateUrl the $compileNodes
          // (or a child element inside of them)
          // might change, so we need to recreate the namespace adapted compileNodes
          // for call to the link function.
          // Note: This will already clone the nodes...
          $linkNode = jqLite(
            wrapTemplate(namespace, jqLite('<div>').append($compileNodes).html())
          );
        } else if (cloneConnectFn) {
          // important!!: we must call our jqLite.clone() since the jQuery one is trying to be smart
          // and sometimes changes the structure of the DOM.
          $linkNode = JQLitePrototype.clone.call($compileNodes);
        } else {
          $linkNode = $compileNodes;
        }

        if (transcludeControllers) {
          for (var controllerName in transcludeControllers) {
            $linkNode.data('$' + controllerName + 'Controller', transcludeControllers[controllerName].instance);
          }
        }

        compile.$$addScopeInfo($linkNode, scope);

        if (cloneConnectFn) cloneConnectFn($linkNode, scope);
        if (compositeLinkFn) compositeLinkFn(scope, $linkNode, $linkNode, parentBoundTranscludeFn);

        if (!cloneConnectFn) {
          $compileNodes = compositeLinkFn = null;
        }
        return $linkNode;
      };
    }

    function detectNamespaceForChildElements(parentElement) {
      // TODO: Make this detect MathML as well...
      var node = parentElement && parentElement[0];
      if (!node) {
        return 'html';
      } else {
        return nodeName_(node) !== 'foreignobject' && toString.call(node).match(/SVG/) ? 'svg' : 'html';
      }
    }

    /**
     * Compile function matches each node in nodeList against the directives. Once all directives
     * for a particular node are collected their compile functions are executed. The compile
     * functions return values - the linking functions - are combined into a composite linking
     * function, which is the a linking function for the node.
     *
     * @param {NodeList} nodeList an array of nodes or NodeList to compile
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *        scope argument is auto-generated to the new child of the transcluded parent scope.
     * @param {DOMElement=} $rootElement If the nodeList is the root of the compilation tree then
     *        the rootElement must be set the jqLite collection of the compile root. This is
     *        needed so that the jqLite collection items can be replaced with widgets.
     * @param {number=} maxPriority Max directive priority.
     * @returns {Function} A composite linking function of all of the matched directives or null.
     */
    function compileNodes(nodeList, transcludeFn, $rootElement, maxPriority, ignoreDirective,
                            previousCompileContext) {
      var linkFns = [],
          // `nodeList` can be either an element's `.childNodes` (live NodeList)
          // or a jqLite/jQuery collection or an array
          notLiveList = isArray(nodeList) || (nodeList instanceof jqLite),
          attrs, directives, nodeLinkFn, childNodes, childLinkFn, linkFnFound, nodeLinkFnFound;


      for (var i = 0; i < nodeList.length; i++) {
        attrs = new Attributes();

        // Support: IE 11 only
        // Workaround for #11781 and #14924
        if (msie === 11) {
          mergeConsecutiveTextNodes(nodeList, i, notLiveList);
        }

        // We must always refer to `nodeList[i]` hereafter,
        // since the nodes can be replaced underneath us.
        directives = collectDirectives(nodeList[i], [], attrs, i === 0 ? maxPriority : undefined,
                                        ignoreDirective);

        nodeLinkFn = (directives.length)
            ? applyDirectivesToNode(directives, nodeList[i], attrs, transcludeFn, $rootElement,
                                      null, [], [], previousCompileContext)
            : null;

        if (nodeLinkFn && nodeLinkFn.scope) {
          compile.$$addScopeClass(attrs.$$element);
        }

        childLinkFn = (nodeLinkFn && nodeLinkFn.terminal ||
                      !(childNodes = nodeList[i].childNodes) ||
                      !childNodes.length)
            ? null
            : compileNodes(childNodes,
                 nodeLinkFn ? (
                  (nodeLinkFn.transcludeOnThisElement || !nodeLinkFn.templateOnThisElement)
                     && nodeLinkFn.transclude) : transcludeFn);

        if (nodeLinkFn || childLinkFn) {
          linkFns.push(i, nodeLinkFn, childLinkFn);
          linkFnFound = true;
          nodeLinkFnFound = nodeLinkFnFound || nodeLinkFn;
        }

        //use the previous context only for the first element in the virtual group
        previousCompileContext = null;
      }

      // return a linking function if we have found anything, null otherwise
      return linkFnFound ? compositeLinkFn : null;

      function compositeLinkFn(scope, nodeList, $rootElement, parentBoundTranscludeFn) {
        var nodeLinkFn, childLinkFn, node, childScope, i, ii, idx, childBoundTranscludeFn;
        var stableNodeList;


        if (nodeLinkFnFound) {
          // copy nodeList so that if a nodeLinkFn removes or adds an element at this DOM level our
          // offsets don't get screwed up
          var nodeListLength = nodeList.length;
          stableNodeList = new Array(nodeListLength);

          // create a sparse array by only copying the elements which have a linkFn
          for (i = 0; i < linkFns.length; i += 3) {
            idx = linkFns[i];
            stableNodeList[idx] = nodeList[idx];
          }
        } else {
          stableNodeList = nodeList;
        }

        for (i = 0, ii = linkFns.length; i < ii;) {
          node = stableNodeList[linkFns[i++]];
          nodeLinkFn = linkFns[i++];
          childLinkFn = linkFns[i++];

          if (nodeLinkFn) {
            if (nodeLinkFn.scope) {
              childScope = scope.$new();
              compile.$$addScopeInfo(jqLite(node), childScope);
            } else {
              childScope = scope;
            }

            if (nodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(
                  scope, nodeLinkFn.transclude, parentBoundTranscludeFn);

            } else if (!nodeLinkFn.templateOnThisElement && parentBoundTranscludeFn) {
              childBoundTranscludeFn = parentBoundTranscludeFn;

            } else if (!parentBoundTranscludeFn && transcludeFn) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, transcludeFn);

            } else {
              childBoundTranscludeFn = null;
            }

            nodeLinkFn(childLinkFn, childScope, node, $rootElement, childBoundTranscludeFn);

          } else if (childLinkFn) {
            childLinkFn(scope, node.childNodes, undefined, parentBoundTranscludeFn);
          }
        }
      }
    }

    function mergeConsecutiveTextNodes(nodeList, idx, notLiveList) {
      var node = nodeList[idx];
      var parent = node.parentNode;
      var sibling;

      if (node.nodeType !== NODE_TYPE_TEXT) {
        return;
      }

      while (true) {
        sibling = parent ? node.nextSibling : nodeList[idx + 1];
        if (!sibling || sibling.nodeType !== NODE_TYPE_TEXT) {
          break;
        }

        node.nodeValue = node.nodeValue + sibling.nodeValue;

        if (sibling.parentNode) {
          sibling.parentNode.removeChild(sibling);
        }
        if (notLiveList && sibling === nodeList[idx + 1]) {
          nodeList.splice(idx + 1, 1);
        }
      }
    }

    function createBoundTranscludeFn(scope, transcludeFn, previousBoundTranscludeFn) {
      function boundTranscludeFn(transcludedScope, cloneFn, controllers, futureParentElement, containingScope) {

        if (!transcludedScope) {
          transcludedScope = scope.$new(false, containingScope);
          transcludedScope.$$transcluded = true;
        }

        return transcludeFn(transcludedScope, cloneFn, {
          parentBoundTranscludeFn: previousBoundTranscludeFn,
          transcludeControllers: controllers,
          futureParentElement: futureParentElement
        });
      }

      // We need  to attach the transclusion slots onto the `boundTranscludeFn`
      // so that they are available inside the `controllersBoundTransclude` function
      var boundSlots = boundTranscludeFn.$$slots = createMap();
      for (var slotName in transcludeFn.$$slots) {
        if (transcludeFn.$$slots[slotName]) {
          boundSlots[slotName] = createBoundTranscludeFn(scope, transcludeFn.$$slots[slotName], previousBoundTranscludeFn);
        } else {
          boundSlots[slotName] = null;
        }
      }

      return boundTranscludeFn;
    }

    /**
     * Looks for directives on the given node and adds them to the directive collection which is
     * sorted.
     *
     * @param node Node to search.
     * @param directives An array to which the directives are added to. This array is sorted before
     *        the function returns.
     * @param attrs The shared attrs object which is used to populate the normalized attributes.
     * @param {number=} maxPriority Max directive priority.
     */
    function collectDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      var nodeType = node.nodeType,
          attrsMap = attrs.$attr,
          match,
          nodeName,
          className;

      switch (nodeType) {
        case NODE_TYPE_ELEMENT: /* Element */

          nodeName = nodeName_(node);

          // use the node name: <directive>
          addDirective(directives,
              directiveNormalize(nodeName), 'E', maxPriority, ignoreDirective);

          // iterate over the attributes
          for (var attr, name, nName, ngAttrName, value, isNgAttr, nAttrs = node.attributes,
                   j = 0, jj = nAttrs && nAttrs.length; j < jj; j++) {
            var attrStartName = false;
            var attrEndName = false;

            attr = nAttrs[j];
            name = attr.name;
            value = attr.value;

            // support ngAttr attribute binding
            ngAttrName = directiveNormalize(name);
            isNgAttr = NG_ATTR_BINDING.test(ngAttrName);
            if (isNgAttr) {
              name = name.replace(PREFIX_REGEXP, '')
                .substr(8).replace(/_(.)/g, function(match, letter) {
                  return letter.toUpperCase();
                });
            }

            var multiElementMatch = ngAttrName.match(MULTI_ELEMENT_DIR_RE);
            if (multiElementMatch && directiveIsMultiElement(multiElementMatch[1])) {
              attrStartName = name;
              attrEndName = name.substr(0, name.length - 5) + 'end';
              name = name.substr(0, name.length - 6);
            }

            nName = directiveNormalize(name.toLowerCase());
            attrsMap[nName] = name;
            if (isNgAttr || !attrs.hasOwnProperty(nName)) {
                attrs[nName] = value;
                if (getBooleanAttrName(node, nName)) {
                  attrs[nName] = true; // presence means true
                }
            }
            addAttrInterpolateDirective(node, directives, value, nName, isNgAttr);
            addDirective(directives, nName, 'A', maxPriority, ignoreDirective, attrStartName,
                          attrEndName);
          }

          if (nodeName === 'input' && node.getAttribute('type') === 'hidden') {
            // Hidden input elements can have strange behaviour when navigating back to the page
            // This tells the browser not to try to cache and reinstate previous values
            node.setAttribute('autocomplete', 'off');
          }

          // use class as directive
          if (!cssClassDirectivesEnabled) break;
          className = node.className;
          if (isObject(className)) {
              // Maybe SVGAnimatedString
              className = className.animVal;
          }
          if (isString(className) && className !== '') {
            while ((match = CLASS_DIRECTIVE_REGEXP.exec(className))) {
              nName = directiveNormalize(match[2]);
              if (addDirective(directives, nName, 'C', maxPriority, ignoreDirective)) {
                attrs[nName] = trim(match[3]);
              }
              className = className.substr(match.index + match[0].length);
            }
          }
          break;
        case NODE_TYPE_TEXT: /* Text Node */
          addTextInterpolateDirective(directives, node.nodeValue);
          break;
        case NODE_TYPE_COMMENT: /* Comment */
          if (!commentDirectivesEnabled) break;
          collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective);
          break;
      }

      directives.sort(byPriority);
      return directives;
    }

    function collectCommentDirectives(node, directives, attrs, maxPriority, ignoreDirective) {
      // function created because of performance, try/catch disables
      // the optimization of the whole function #14848
      try {
        var match = COMMENT_DIRECTIVE_REGEXP.exec(node.nodeValue);
        if (match) {
          var nName = directiveNormalize(match[1]);
          if (addDirective(directives, nName, 'M', maxPriority, ignoreDirective)) {
            attrs[nName] = trim(match[2]);
          }
        }
      } catch (e) {
        // turns out that under some circumstances IE9 throws errors when one attempts to read
        // comment's node value.
        // Just ignore it and continue. (Can't seem to reproduce in test case.)
      }
    }

    /**
     * Given a node with a directive-start it collects all of the siblings until it finds
     * directive-end.
     * @param node
     * @param attrStart
     * @param attrEnd
     * @returns {*}
     */
    function groupScan(node, attrStart, attrEnd) {
      var nodes = [];
      var depth = 0;
      if (attrStart && node.hasAttribute && node.hasAttribute(attrStart)) {
        do {
          if (!node) {
            throw $compileMinErr('uterdir',
                      'Unterminated attribute, found \'{0}\' but no matching \'{1}\' found.',
                      attrStart, attrEnd);
          }
          if (node.nodeType === NODE_TYPE_ELEMENT) {
            if (node.hasAttribute(attrStart)) depth++;
            if (node.hasAttribute(attrEnd)) depth--;
          }
          nodes.push(node);
          node = node.nextSibling;
        } while (depth > 0);
      } else {
        nodes.push(node);
      }

      return jqLite(nodes);
    }

    /**
     * Wrapper for linking function which converts normal linking function into a grouped
     * linking function.
     * @param linkFn
     * @param attrStart
     * @param attrEnd
     * @returns {Function}
     */
    function groupElementsLinkFnWrapper(linkFn, attrStart, attrEnd) {
      return function groupedElementsLink(scope, element, attrs, controllers, transcludeFn) {
        element = groupScan(element[0], attrStart, attrEnd);
        return linkFn(scope, element, attrs, controllers, transcludeFn);
      };
    }

    /**
     * A function generator that is used to support both eager and lazy compilation
     * linking function.
     * @param eager
     * @param $compileNodes
     * @param transcludeFn
     * @param maxPriority
     * @param ignoreDirective
     * @param previousCompileContext
     * @returns {Function}
     */
    function compilationGenerator(eager, $compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext) {
      var compiled;

      if (eager) {
        return compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);
      }
      return /** @this */ function lazyCompilation() {
        if (!compiled) {
          compiled = compile($compileNodes, transcludeFn, maxPriority, ignoreDirective, previousCompileContext);

          // Null out all of these references in order to make them eligible for garbage collection
          // since this is a potentially long lived closure
          $compileNodes = transcludeFn = previousCompileContext = null;
        }
        return compiled.apply(this, arguments);
      };
    }

    /**
     * Once the directives have been collected, their compile functions are executed. This method
     * is responsible for inlining directive templates as well as terminating the application
     * of the directives if the terminal directive has been reached.
     *
     * @param {Array} directives Array of collected directives to execute their compile function.
     *        this needs to be pre-sorted by priority order.
     * @param {Node} compileNode The raw DOM node to apply the compile functions to
     * @param {Object} templateAttrs The shared attribute function
     * @param {function(angular.Scope, cloneAttachFn=)} transcludeFn A linking function, where the
     *                                                  scope argument is auto-generated to the new
     *                                                  child of the transcluded parent scope.
     * @param {JQLite} jqCollection If we are working on the root of the compile tree then this
     *                              argument has the root jqLite array so that we can replace nodes
     *                              on it.
     * @param {Object=} originalReplaceDirective An optional directive that will be ignored when
     *                                           compiling the transclusion.
     * @param {Array.<Function>} preLinkFns
     * @param {Array.<Function>} postLinkFns
     * @param {Object} previousCompileContext Context used for previous compilation of the current
     *                                        node
     * @returns {Function} linkFn
     */
    function applyDirectivesToNode(directives, compileNode, templateAttrs, transcludeFn,
                                   jqCollection, originalReplaceDirective, preLinkFns, postLinkFns,
                                   previousCompileContext) {
      previousCompileContext = previousCompileContext || {};

      var terminalPriority = -Number.MAX_VALUE,
          newScopeDirective = previousCompileContext.newScopeDirective,
          controllerDirectives = previousCompileContext.controllerDirectives,
          newIsolateScopeDirective = previousCompileContext.newIsolateScopeDirective,
          templateDirective = previousCompileContext.templateDirective,
          nonTlbTranscludeDirective = previousCompileContext.nonTlbTranscludeDirective,
          hasTranscludeDirective = false,
          hasTemplate = false,
          hasElementTranscludeDirective = previousCompileContext.hasElementTranscludeDirective,
          $compileNode = templateAttrs.$$element = jqLite(compileNode),
          directive,
          directiveName,
          $template,
          replaceDirective = originalReplaceDirective,
          childTranscludeFn = transcludeFn,
          linkFn,
          didScanForMultipleTransclusion = false,
          mightHaveMultipleTransclusionError = false,
          directiveValue;

      // executes all directives on the current element
      for (var i = 0, ii = directives.length; i < ii; i++) {
        directive = directives[i];
        var attrStart = directive.$$start;
        var attrEnd = directive.$$end;

        // collect multiblock sections
        if (attrStart) {
          $compileNode = groupScan(compileNode, attrStart, attrEnd);
        }
        $template = undefined;

        if (terminalPriority > directive.priority) {
          break; // prevent further processing of directives
        }

        directiveValue = directive.scope;

        if (directiveValue) {

          // skip the check for directives with async templates, we'll check the derived sync
          // directive when the template arrives
          if (!directive.templateUrl) {
            if (isObject(directiveValue)) {
              // This directive is trying to add an isolated scope.
              // Check that there is no scope of any kind already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective || newScopeDirective,
                                directive, $compileNode);
              newIsolateScopeDirective = directive;
            } else {
              // This directive is trying to add a child scope.
              // Check that there is no isolated scope already
              assertNoDuplicate('new/isolated scope', newIsolateScopeDirective, directive,
                                $compileNode);
            }
          }

          newScopeDirective = newScopeDirective || directive;
        }

        directiveName = directive.name;

        // If we encounter a condition that can result in transclusion on the directive,
        // then scan ahead in the remaining directives for others that may cause a multiple
        // transclusion error to be thrown during the compilation process.  If a matching directive
        // is found, then we know that when we encounter a transcluded directive, we need to eagerly
        // compile the `transclude` function rather than doing it lazily in order to throw
        // exceptions at the correct time
        if (!didScanForMultipleTransclusion && ((directive.replace && (directive.templateUrl || directive.template))
            || (directive.transclude && !directive.$$tlb))) {
                var candidateDirective;

                for (var scanningIndex = i + 1; (candidateDirective = directives[scanningIndex++]);) {
                    if ((candidateDirective.transclude && !candidateDirective.$$tlb)
                        || (candidateDirective.replace && (candidateDirective.templateUrl || candidateDirective.template))) {
                        mightHaveMultipleTransclusionError = true;
                        break;
                    }
                }

                didScanForMultipleTransclusion = true;
        }

        if (!directive.templateUrl && directive.controller) {
          controllerDirectives = controllerDirectives || createMap();
          assertNoDuplicate('\'' + directiveName + '\' controller',
              controllerDirectives[directiveName], directive, $compileNode);
          controllerDirectives[directiveName] = directive;
        }

        directiveValue = directive.transclude;

        if (directiveValue) {
          hasTranscludeDirective = true;

          // Special case ngIf and ngRepeat so that we don't complain about duplicate transclusion.
          // This option should only be used by directives that know how to safely handle element transclusion,
          // where the transcluded nodes are added or replaced after linking.
          if (!directive.$$tlb) {
            assertNoDuplicate('transclusion', nonTlbTranscludeDirective, directive, $compileNode);
            nonTlbTranscludeDirective = directive;
          }

          if (directiveValue === 'element') {
            hasElementTranscludeDirective = true;
            terminalPriority = directive.priority;
            $template = $compileNode;
            $compileNode = templateAttrs.$$element =
                jqLite(compile.$$createComment(directiveName, templateAttrs[directiveName]));
            compileNode = $compileNode[0];
            replaceWith(jqCollection, sliceArgs($template), compileNode);

            // Support: Chrome < 50
            // https://github.com/angular/angular.js/issues/14041

            // In the versions of V8 prior to Chrome 50, the document fragment that is created
            // in the `replaceWith` function is improperly garbage collected despite still
            // being referenced by the `parentNode` property of all of the child nodes.  By adding
            // a reference to the fragment via a different property, we can avoid that incorrect
            // behavior.
            // TODO: remove this line after Chrome 50 has been released
            $template[0].$$parentNode = $template[0].parentNode;

            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, terminalPriority,
                                        replaceDirective && replaceDirective.name, {
                                          // Don't pass in:
                                          // - controllerDirectives - otherwise we'll create duplicates controllers
                                          // - newIsolateScopeDirective or templateDirective - combining templates with
                                          //   element transclusion doesn't make sense.
                                          //
                                          // We need only nonTlbTranscludeDirective so that we prevent putting transclusion
                                          // on the same element more than once.
                                          nonTlbTranscludeDirective: nonTlbTranscludeDirective
                                        });
          } else {

            var slots = createMap();

            if (!isObject(directiveValue)) {
              $template = jqLite(jqLiteClone(compileNode)).contents();
            } else {

              // We have transclusion slots,
              // collect them up, compile them and store their transclusion functions
              $template = [];

              var slotMap = createMap();
              var filledSlots = createMap();

              // Parse the element selectors
              forEach(directiveValue, function(elementSelector, slotName) {
                // If an element selector starts with a ? then it is optional
                var optional = (elementSelector.charAt(0) === '?');
                elementSelector = optional ? elementSelector.substring(1) : elementSelector;

                slotMap[elementSelector] = slotName;

                // We explicitly assign `null` since this implies that a slot was defined but not filled.
                // Later when calling boundTransclusion functions with a slot name we only error if the
                // slot is `undefined`
                slots[slotName] = null;

                // filledSlots contains `true` for all slots that are either optional or have been
                // filled. This is used to check that we have not missed any required slots
                filledSlots[slotName] = optional;
              });

              // Add the matching elements into their slot
              forEach($compileNode.contents(), function(node) {
                var slotName = slotMap[directiveNormalize(nodeName_(node))];
                if (slotName) {
                  filledSlots[slotName] = true;
                  slots[slotName] = slots[slotName] || [];
                  slots[slotName].push(node);
                } else {
                  $template.push(node);
                }
              });

              // Check for required slots that were not filled
              forEach(filledSlots, function(filled, slotName) {
                if (!filled) {
                  throw $compileMinErr('reqslot', 'Required transclusion slot `{0}` was not filled.', slotName);
                }
              });

              for (var slotName in slots) {
                if (slots[slotName]) {
                  // Only define a transclusion function if the slot was filled
                  slots[slotName] = compilationGenerator(mightHaveMultipleTransclusionError, slots[slotName], transcludeFn);
                }
              }
            }

            $compileNode.empty(); // clear contents
            childTranscludeFn = compilationGenerator(mightHaveMultipleTransclusionError, $template, transcludeFn, undefined,
                undefined, { needsNewScope: directive.$$isolateScope || directive.$$newScope});
            childTranscludeFn.$$slots = slots;
          }
        }

        if (directive.template) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          directiveValue = (isFunction(directive.template))
              ? directive.template($compileNode, templateAttrs)
              : directive.template;

          directiveValue = denormalizeTemplate(directiveValue);

          if (directive.replace) {
            replaceDirective = directive;
            if (jqLiteIsTextNode(directiveValue)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(directive.templateNamespace, trim(directiveValue)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  directiveName, '');
            }

            replaceWith(jqCollection, $compileNode, compileNode);

            var newTemplateAttrs = {$attr: {}};

            // combine directives from the original node and from the template:
            // - take the array of directives for this element
            // - split it into two parts, those that already applied (processed) and those that weren't (unprocessed)
            // - collect directives from the template and sort them by priority
            // - combine directives as: processed + template + unprocessed
            var templateDirectives = collectDirectives(compileNode, [], newTemplateAttrs);
            var unprocessedDirectives = directives.splice(i + 1, directives.length - (i + 1));

            if (newIsolateScopeDirective || newScopeDirective) {
              // The original directive caused the current element to be replaced but this element
              // also needs to have a new scope, so we need to tell the template directives
              // that they would need to get their scope from further up, if they require transclusion
              markDirectiveScope(templateDirectives, newIsolateScopeDirective, newScopeDirective);
            }
            directives = directives.concat(templateDirectives).concat(unprocessedDirectives);
            mergeTemplateAttributes(templateAttrs, newTemplateAttrs);

            ii = directives.length;
          } else {
            $compileNode.html(directiveValue);
          }
        }

        if (directive.templateUrl) {
          hasTemplate = true;
          assertNoDuplicate('template', templateDirective, directive, $compileNode);
          templateDirective = directive;

          if (directive.replace) {
            replaceDirective = directive;
          }

          // eslint-disable-next-line no-func-assign
          nodeLinkFn = compileTemplateUrl(directives.splice(i, directives.length - i), $compileNode,
              templateAttrs, jqCollection, hasTranscludeDirective && childTranscludeFn, preLinkFns, postLinkFns, {
                controllerDirectives: controllerDirectives,
                newScopeDirective: (newScopeDirective !== directive) && newScopeDirective,
                newIsolateScopeDirective: newIsolateScopeDirective,
                templateDirective: templateDirective,
                nonTlbTranscludeDirective: nonTlbTranscludeDirective
              });
          ii = directives.length;
        } else if (directive.compile) {
          try {
            linkFn = directive.compile($compileNode, templateAttrs, childTranscludeFn);
            var context = directive.$$originalDirective || directive;
            if (isFunction(linkFn)) {
              addLinkFns(null, bind(context, linkFn), attrStart, attrEnd);
            } else if (linkFn) {
              addLinkFns(bind(context, linkFn.pre), bind(context, linkFn.post), attrStart, attrEnd);
            }
          } catch (e) {
            $exceptionHandler(e, startingTag($compileNode));
          }
        }

        if (directive.terminal) {
          nodeLinkFn.terminal = true;
          terminalPriority = Math.max(terminalPriority, directive.priority);
        }

      }

      nodeLinkFn.scope = newScopeDirective && newScopeDirective.scope === true;
      nodeLinkFn.transcludeOnThisElement = hasTranscludeDirective;
      nodeLinkFn.templateOnThisElement = hasTemplate;
      nodeLinkFn.transclude = childTranscludeFn;

      previousCompileContext.hasElementTranscludeDirective = hasElementTranscludeDirective;

      // might be normal or delayed nodeLinkFn depending on if templateUrl is present
      return nodeLinkFn;

      ////////////////////

      function addLinkFns(pre, post, attrStart, attrEnd) {
        if (pre) {
          if (attrStart) pre = groupElementsLinkFnWrapper(pre, attrStart, attrEnd);
          pre.require = directive.require;
          pre.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            pre = cloneAndAnnotateFn(pre, {isolateScope: true});
          }
          preLinkFns.push(pre);
        }
        if (post) {
          if (attrStart) post = groupElementsLinkFnWrapper(post, attrStart, attrEnd);
          post.require = directive.require;
          post.directiveName = directiveName;
          if (newIsolateScopeDirective === directive || directive.$$isolateScope) {
            post = cloneAndAnnotateFn(post, {isolateScope: true});
          }
          postLinkFns.push(post);
        }
      }

      function nodeLinkFn(childLinkFn, scope, linkNode, $rootElement, boundTranscludeFn) {
        var i, ii, linkFn, isolateScope, controllerScope, elementControllers, transcludeFn, $element,
            attrs, scopeBindingInfo;

        if (compileNode === linkNode) {
          attrs = templateAttrs;
          $element = templateAttrs.$$element;
        } else {
          $element = jqLite(linkNode);
          attrs = new Attributes($element, templateAttrs);
        }

        controllerScope = scope;
        if (newIsolateScopeDirective) {
          isolateScope = scope.$new(true);
        } else if (newScopeDirective) {
          controllerScope = scope.$parent;
        }

        if (boundTranscludeFn) {
          // track `boundTranscludeFn` so it can be unwrapped if `transcludeFn`
          // is later passed as `parentBoundTranscludeFn` to `publicLinkFn`
          transcludeFn = controllersBoundTransclude;
          transcludeFn.$$boundTransclude = boundTranscludeFn;
          // expose the slots on the `$transclude` function
          transcludeFn.isSlotFilled = function(slotName) {
            return !!boundTranscludeFn.$$slots[slotName];
          };
        }

        if (controllerDirectives) {
          elementControllers = setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective);
        }

        if (newIsolateScopeDirective) {
          // Initialize isolate scope bindings for new isolate scope directive.
          compile.$$addScopeInfo($element, isolateScope, true, !(templateDirective && (templateDirective === newIsolateScopeDirective ||
              templateDirective === newIsolateScopeDirective.$$originalDirective)));
          compile.$$addScopeClass($element, true);
          isolateScope.$$isolateBindings =
              newIsolateScopeDirective.$$isolateBindings;
          scopeBindingInfo = initializeDirectiveBindings(scope, attrs, isolateScope,
                                        isolateScope.$$isolateBindings,
                                        newIsolateScopeDirective);
          if (scopeBindingInfo.removeWatches) {
            isolateScope.$on('$destroy', scopeBindingInfo.removeWatches);
          }
        }

        // Initialize bindToController bindings
        for (var name in elementControllers) {
          var controllerDirective = controllerDirectives[name];
          var controller = elementControllers[name];
          var bindings = controllerDirective.$$bindings.bindToController;

          if (preAssignBindingsEnabled) {
            if (bindings) {
              controller.bindingInfo =
                initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
            } else {
              controller.bindingInfo = {};
            }

            var controllerResult = controller();
            if (controllerResult !== controller.instance) {
              // If the controller constructor has a return value, overwrite the instance
              // from setupControllers
              controller.instance = controllerResult;
              $element.data('$' + controllerDirective.name + 'Controller', controllerResult);
              if (controller.bindingInfo.removeWatches) {
                controller.bindingInfo.removeWatches();
              }
              controller.bindingInfo =
                initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
            }
          } else {
            controller.instance = controller();
            $element.data('$' + controllerDirective.name + 'Controller', controller.instance);
            controller.bindingInfo =
              initializeDirectiveBindings(controllerScope, attrs, controller.instance, bindings, controllerDirective);
          }
        }

        // Bind the required controllers to the controller, if `require` is an object and `bindToController` is truthy
        forEach(controllerDirectives, function(controllerDirective, name) {
          var require = controllerDirective.require;
          if (controllerDirective.bindToController && !isArray(require) && isObject(require)) {
            extend(elementControllers[name].instance, getControllers(name, require, $element, elementControllers));
          }
        });

        // Handle the init and destroy lifecycle hooks on all controllers that have them
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$onChanges)) {
            try {
              controllerInstance.$onChanges(controller.bindingInfo.initialChanges);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$onInit)) {
            try {
              controllerInstance.$onInit();
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          if (isFunction(controllerInstance.$doCheck)) {
            controllerScope.$watch(function() { controllerInstance.$doCheck(); });
            controllerInstance.$doCheck();
          }
          if (isFunction(controllerInstance.$onDestroy)) {
            controllerScope.$on('$destroy', function callOnDestroyHook() {
              controllerInstance.$onDestroy();
            });
          }
        });

        // PRELINKING
        for (i = 0, ii = preLinkFns.length; i < ii; i++) {
          linkFn = preLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // RECURSION
        // We only pass the isolate scope, if the isolate directive has a template,
        // otherwise the child elements do not belong to the isolate directive.
        var scopeToChild = scope;
        if (newIsolateScopeDirective && (newIsolateScopeDirective.template || newIsolateScopeDirective.templateUrl === null)) {
          scopeToChild = isolateScope;
        }
        if (childLinkFn) {
          childLinkFn(scopeToChild, linkNode.childNodes, undefined, boundTranscludeFn);
        }

        // POSTLINKING
        for (i = postLinkFns.length - 1; i >= 0; i--) {
          linkFn = postLinkFns[i];
          invokeLinkFn(linkFn,
              linkFn.isolateScope ? isolateScope : scope,
              $element,
              attrs,
              linkFn.require && getControllers(linkFn.directiveName, linkFn.require, $element, elementControllers),
              transcludeFn
          );
        }

        // Trigger $postLink lifecycle hooks
        forEach(elementControllers, function(controller) {
          var controllerInstance = controller.instance;
          if (isFunction(controllerInstance.$postLink)) {
            controllerInstance.$postLink();
          }
        });

        // This is the function that is injected as `$transclude`.
        // Note: all arguments are optional!
        function controllersBoundTransclude(scope, cloneAttachFn, futureParentElement, slotName) {
          var transcludeControllers;
          // No scope passed in:
          if (!isScope(scope)) {
            slotName = futureParentElement;
            futureParentElement = cloneAttachFn;
            cloneAttachFn = scope;
            scope = undefined;
          }

          if (hasElementTranscludeDirective) {
            transcludeControllers = elementControllers;
          }
          if (!futureParentElement) {
            futureParentElement = hasElementTranscludeDirective ? $element.parent() : $element;
          }
          if (slotName) {
            // slotTranscludeFn can be one of three things:
            //  * a transclude function - a filled slot
            //  * `null` - an optional slot that was not filled
            //  * `undefined` - a slot that was not declared (i.e. invalid)
            var slotTranscludeFn = boundTranscludeFn.$$slots[slotName];
            if (slotTranscludeFn) {
              return slotTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
            } else if (isUndefined(slotTranscludeFn)) {
              throw $compileMinErr('noslot',
               'No parent directive that requires a transclusion with slot name "{0}". ' +
               'Element: {1}',
               slotName, startingTag($element));
            }
          } else {
            return boundTranscludeFn(scope, cloneAttachFn, transcludeControllers, futureParentElement, scopeToChild);
          }
        }
      }
    }

    function getControllers(directiveName, require, $element, elementControllers) {
      var value;

      if (isString(require)) {
        var match = require.match(REQUIRE_PREFIX_REGEXP);
        var name = require.substring(match[0].length);
        var inheritType = match[1] || match[3];
        var optional = match[2] === '?';

        //If only parents then start at the parent element
        if (inheritType === '^^') {
          $element = $element.parent();
        //Otherwise attempt getting the controller from elementControllers in case
        //the element is transcluded (and has no data) and to avoid .data if possible
        } else {
          value = elementControllers && elementControllers[name];
          value = value && value.instance;
        }

        if (!value) {
          var dataName = '$' + name + 'Controller';
          value = inheritType ? $element.inheritedData(dataName) : $element.data(dataName);
        }

        if (!value && !optional) {
          throw $compileMinErr('ctreq',
              'Controller \'{0}\', required by directive \'{1}\', can\'t be found!',
              name, directiveName);
        }
      } else if (isArray(require)) {
        value = [];
        for (var i = 0, ii = require.length; i < ii; i++) {
          value[i] = getControllers(directiveName, require[i], $element, elementControllers);
        }
      } else if (isObject(require)) {
        value = {};
        forEach(require, function(controller, property) {
          value[property] = getControllers(directiveName, controller, $element, elementControllers);
        });
      }

      return value || null;
    }

    function setupControllers($element, attrs, transcludeFn, controllerDirectives, isolateScope, scope, newIsolateScopeDirective) {
      var elementControllers = createMap();
      for (var controllerKey in controllerDirectives) {
        var directive = controllerDirectives[controllerKey];
        var locals = {
          $scope: directive === newIsolateScopeDirective || directive.$$isolateScope ? isolateScope : scope,
          $element: $element,
          $attrs: attrs,
          $transclude: transcludeFn
        };

        var controller = directive.controller;
        if (controller === '@') {
          controller = attrs[directive.name];
        }

        var controllerInstance = $controller(controller, locals, true, directive.controllerAs);

        // For directives with element transclusion the element is a comment.
        // In this case .data will not attach any data.
        // Instead, we save the controllers for the element in a local hash and attach to .data
        // later, once we have the actual element.
        elementControllers[directive.name] = controllerInstance;
        $element.data('$' + directive.name + 'Controller', controllerInstance.instance);
      }
      return elementControllers;
    }

    // Depending upon the context in which a directive finds itself it might need to have a new isolated
    // or child scope created. For instance:
    // * if the directive has been pulled into a template because another directive with a higher priority
    // asked for element transclusion
    // * if the directive itself asks for transclusion but it is at the root of a template and the original
    // element was replaced. See https://github.com/angular/angular.js/issues/12936
    function markDirectiveScope(directives, isolateScope, newScope) {
      for (var j = 0, jj = directives.length; j < jj; j++) {
        directives[j] = inherit(directives[j], {$$isolateScope: isolateScope, $$newScope: newScope});
      }
    }

    /**
     * looks up the directive and decorates it with exception handling and proper parameters. We
     * call this the boundDirective.
     *
     * @param {string} name name of the directive to look up.
     * @param {string} location The directive must be found in specific format.
     *   String containing any of theses characters:
     *
     *   * `E`: element name
     *   * `A': attribute
     *   * `C`: class
     *   * `M`: comment
     * @returns {boolean} true if directive was added.
     */
    function addDirective(tDirectives, name, location, maxPriority, ignoreDirective, startAttrName,
                          endAttrName) {
      if (name === ignoreDirective) return null;
      var match = null;
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if ((isUndefined(maxPriority) || maxPriority > directive.priority) &&
               directive.restrict.indexOf(location) !== -1) {
            if (startAttrName) {
              directive = inherit(directive, {$$start: startAttrName, $$end: endAttrName});
            }
            if (!directive.$$bindings) {
              var bindings = directive.$$bindings =
                  parseDirectiveBindings(directive, directive.name);
              if (isObject(bindings.isolateScope)) {
                directive.$$isolateBindings = bindings.isolateScope;
              }
            }
            tDirectives.push(directive);
            match = directive;
          }
        }
      }
      return match;
    }


    /**
     * looks up the directive and returns true if it is a multi-element directive,
     * and therefore requires DOM nodes between -start and -end markers to be grouped
     * together.
     *
     * @param {string} name name of the directive to look up.
     * @returns true if directive was registered as multi-element.
     */
    function directiveIsMultiElement(name) {
      if (hasDirectives.hasOwnProperty(name)) {
        for (var directive, directives = $injector.get(name + Suffix),
            i = 0, ii = directives.length; i < ii; i++) {
          directive = directives[i];
          if (directive.multiElement) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * When the element is replaced with HTML template then the new attributes
     * on the template need to be merged with the existing attributes in the DOM.
     * The desired effect is to have both of the attributes present.
     *
     * @param {object} dst destination attributes (original DOM)
     * @param {object} src source attributes (from the directive template)
     */
    function mergeTemplateAttributes(dst, src) {
      var srcAttr = src.$attr,
          dstAttr = dst.$attr;

      // reapply the old attributes to the new element
      forEach(dst, function(value, key) {
        if (key.charAt(0) !== '$') {
          if (src[key] && src[key] !== value) {
            if (value.length) {
              value += (key === 'style' ? ';' : ' ') + src[key];
            } else {
              value = src[key];
            }
          }
          dst.$set(key, value, true, srcAttr[key]);
        }
      });

      // copy the new attributes on the old attrs object
      forEach(src, function(value, key) {
        // Check if we already set this attribute in the loop above.
        // `dst` will never contain hasOwnProperty as DOM parser won't let it.
        // You will get an "InvalidCharacterError: DOM Exception 5" error if you
        // have an attribute like "has-own-property" or "data-has-own-property", etc.
        if (!dst.hasOwnProperty(key) && key.charAt(0) !== '$') {
          dst[key] = value;

          if (key !== 'class' && key !== 'style') {
            dstAttr[key] = srcAttr[key];
          }
        }
      });
    }


    function compileTemplateUrl(directives, $compileNode, tAttrs,
        $rootElement, childTranscludeFn, preLinkFns, postLinkFns, previousCompileContext) {
      var linkQueue = [],
          afterTemplateNodeLinkFn,
          afterTemplateChildLinkFn,
          beforeTemplateCompileNode = $compileNode[0],
          origAsyncDirective = directives.shift(),
          derivedSyncDirective = inherit(origAsyncDirective, {
            templateUrl: null, transclude: null, replace: null, $$originalDirective: origAsyncDirective
          }),
          templateUrl = (isFunction(origAsyncDirective.templateUrl))
              ? origAsyncDirective.templateUrl($compileNode, tAttrs)
              : origAsyncDirective.templateUrl,
          templateNamespace = origAsyncDirective.templateNamespace;

      $compileNode.empty();

      $templateRequest(templateUrl)
        .then(function(content) {
          var compileNode, tempTemplateAttrs, $template, childBoundTranscludeFn;

          content = denormalizeTemplate(content);

          if (origAsyncDirective.replace) {
            if (jqLiteIsTextNode(content)) {
              $template = [];
            } else {
              $template = removeComments(wrapTemplate(templateNamespace, trim(content)));
            }
            compileNode = $template[0];

            if ($template.length !== 1 || compileNode.nodeType !== NODE_TYPE_ELEMENT) {
              throw $compileMinErr('tplrt',
                  'Template for directive \'{0}\' must have exactly one root element. {1}',
                  origAsyncDirective.name, templateUrl);
            }

            tempTemplateAttrs = {$attr: {}};
            replaceWith($rootElement, $compileNode, compileNode);
            var templateDirectives = collectDirectives(compileNode, [], tempTemplateAttrs);

            if (isObject(origAsyncDirective.scope)) {
              // the original directive that caused the template to be loaded async required
              // an isolate scope
              markDirectiveScope(templateDirectives, true);
            }
            directives = templateDirectives.concat(directives);
            mergeTemplateAttributes(tAttrs, tempTemplateAttrs);
          } else {
            compileNode = beforeTemplateCompileNode;
            $compileNode.html(content);
          }

          directives.unshift(derivedSyncDirective);

          afterTemplateNodeLinkFn = applyDirectivesToNode(directives, compileNode, tAttrs,
              childTranscludeFn, $compileNode, origAsyncDirective, preLinkFns, postLinkFns,
              previousCompileContext);
          forEach($rootElement, function(node, i) {
            if (node === compileNode) {
              $rootElement[i] = $compileNode[0];
            }
          });
          afterTemplateChildLinkFn = compileNodes($compileNode[0].childNodes, childTranscludeFn);

          while (linkQueue.length) {
            var scope = linkQueue.shift(),
                beforeTemplateLinkNode = linkQueue.shift(),
                linkRootElement = linkQueue.shift(),
                boundTranscludeFn = linkQueue.shift(),
                linkNode = $compileNode[0];

            if (scope.$$destroyed) continue;

            if (beforeTemplateLinkNode !== beforeTemplateCompileNode) {
              var oldClasses = beforeTemplateLinkNode.className;

              if (!(previousCompileContext.hasElementTranscludeDirective &&
                  origAsyncDirective.replace)) {
                // it was cloned therefore we have to clone as well.
                linkNode = jqLiteClone(compileNode);
              }
              replaceWith(linkRootElement, jqLite(beforeTemplateLinkNode), linkNode);

              // Copy in CSS classes from original node
              safeAddClass(jqLite(linkNode), oldClasses);
            }
            if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
              childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
            } else {
              childBoundTranscludeFn = boundTranscludeFn;
            }
            afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, linkNode, $rootElement,
              childBoundTranscludeFn);
          }
          linkQueue = null;
        }).catch(function(error) {
          if (isError(error)) {
            $exceptionHandler(error);
          }
        });

      return function delayedNodeLinkFn(ignoreChildLinkFn, scope, node, rootElement, boundTranscludeFn) {
        var childBoundTranscludeFn = boundTranscludeFn;
        if (scope.$$destroyed) return;
        if (linkQueue) {
          linkQueue.push(scope,
                         node,
                         rootElement,
                         childBoundTranscludeFn);
        } else {
          if (afterTemplateNodeLinkFn.transcludeOnThisElement) {
            childBoundTranscludeFn = createBoundTranscludeFn(scope, afterTemplateNodeLinkFn.transclude, boundTranscludeFn);
          }
          afterTemplateNodeLinkFn(afterTemplateChildLinkFn, scope, node, rootElement, childBoundTranscludeFn);
        }
      };
    }


    /**
     * Sorting function for bound directives.
     */
    function byPriority(a, b) {
      var diff = b.priority - a.priority;
      if (diff !== 0) return diff;
      if (a.name !== b.name) return (a.name < b.name) ? -1 : 1;
      return a.index - b.index;
    }

    function assertNoDuplicate(what, previousDirective, directive, element) {

      function wrapModuleNameIfDefined(moduleName) {
        return moduleName ?
          (' (module: ' + moduleName + ')') :
          '';
      }

      if (previousDirective) {
        throw $compileMinErr('multidir', 'Multiple directives [{0}{1}, {2}{3}] asking for {4} on: {5}',
            previousDirective.name, wrapModuleNameIfDefined(previousDirective.$$moduleName),
            directive.name, wrapModuleNameIfDefined(directive.$$moduleName), what, startingTag(element));
      }
    }


    function addTextInterpolateDirective(directives, text) {
      var interpolateFn = $interpolate(text, true);
      if (interpolateFn) {
        directives.push({
          priority: 0,
          compile: function textInterpolateCompileFn(templateNode) {
            var templateNodeParent = templateNode.parent(),
                hasCompileParent = !!templateNodeParent.length;

            // When transcluding a template that has bindings in the root
            // we don't have a parent and thus need to add the class during linking fn.
            if (hasCompileParent) compile.$$addBindingClass(templateNodeParent);

            return function textInterpolateLinkFn(scope, node) {
              var parent = node.parent();
              if (!hasCompileParent) compile.$$addBindingClass(parent);
              compile.$$addBindingInfo(parent, interpolateFn.expressions);
              scope.$watch(interpolateFn, function interpolateFnWatchAction(value) {
                node[0].nodeValue = value;
              });
            };
          }
        });
      }
    }


    function wrapTemplate(type, template) {
      type = lowercase(type || 'html');
      switch (type) {
      case 'svg':
      case 'math':
        var wrapper = window.document.createElement('div');
        wrapper.innerHTML = '<' + type + '>' + template + '</' + type + '>';
        return wrapper.childNodes[0].childNodes;
      default:
        return template;
      }
    }


    function getTrustedContext(node, attrNormalizedName) {
      if (attrNormalizedName === 'srcdoc') {
        return $sce.HTML;
      }
      var tag = nodeName_(node);
      // All tags with src attributes require a RESOURCE_URL value, except for
      // img and various html5 media tags.
      if (attrNormalizedName === 'src' || attrNormalizedName === 'ngSrc') {
        if (['img', 'video', 'audio', 'source', 'track'].indexOf(tag) === -1) {
          return $sce.RESOURCE_URL;
        }
      // maction[xlink:href] can source SVG.  It's not limited to <maction>.
      } else if (attrNormalizedName === 'xlinkHref' ||
          (tag === 'form' && attrNormalizedName === 'action') ||
          // links can be stylesheets or imports, which can run script in the current origin
          (tag === 'link' && attrNormalizedName === 'href')
      ) {
        return $sce.RESOURCE_URL;
      }
    }


    function addAttrInterpolateDirective(node, directives, value, name, isNgAttr) {
      var trustedContext = getTrustedContext(node, name);
      var mustHaveExpression = !isNgAttr;
      var allOrNothing = ALL_OR_NOTHING_ATTRS[name] || isNgAttr;

      var interpolateFn = $interpolate(value, mustHaveExpression, trustedContext, allOrNothing);

      // no interpolation found -> ignore
      if (!interpolateFn) return;

      if (name === 'multiple' && nodeName_(node) === 'select') {
        throw $compileMinErr('selmulti',
            'Binding to the \'multiple\' attribute is not supported. Element: {0}',
            startingTag(node));
      }

      if (EVENT_HANDLER_ATTR_REGEXP.test(name)) {
        throw $compileMinErr('nodomevents',
            'Interpolations for HTML DOM event attributes are disallowed.  Please use the ' +
                'ng- versions (such as ng-click instead of onclick) instead.');
      }

      directives.push({
        priority: 100,
        compile: function() {
            return {
              pre: function attrInterpolatePreLinkFn(scope, element, attr) {
                var $$observers = (attr.$$observers || (attr.$$observers = createMap()));

                // If the attribute has changed since last $interpolate()ed
                var newValue = attr[name];
                if (newValue !== value) {
                  // we need to interpolate again since the attribute value has been updated
                  // (e.g. by another directive's compile function)
                  // ensure unset/empty values make interpolateFn falsy
                  interpolateFn = newValue && $interpolate(newValue, true, trustedContext, allOrNothing);
                  value = newValue;
                }

                // if attribute was updated so that there is no interpolation going on we don't want to
                // register any observers
                if (!interpolateFn) return;

                // initialize attr object so that it's ready in case we need the value for isolate
                // scope initialization, otherwise the value would not be available from isolate
                // directive's linking fn during linking phase
                attr[name] = interpolateFn(scope);

                ($$observers[name] || ($$observers[name] = [])).$$inter = true;
                (attr.$$observers && attr.$$observers[name].$$scope || scope).
                  $watch(interpolateFn, function interpolateFnWatchAction(newValue, oldValue) {
                    //special case for class attribute addition + removal
                    //so that class changes can tap into the animation
                    //hooks provided by the $animate service. Be sure to
                    //skip animations when the first digest occurs (when
                    //both the new and the old values are the same) since
                    //the CSS classes are the non-interpolated values
                    if (name === 'class' && newValue !== oldValue) {
                      attr.$updateClass(newValue, oldValue);
                    } else {
                      attr.$set(name, newValue);
                    }
                  });
              }
            };
          }
      });
    }


    /**
     * This is a special jqLite.replaceWith, which can replace items which
     * have no parents, provided that the containing jqLite collection is provided.
     *
     * @param {JqLite=} $rootElement The root of the compile tree. Used so that we can replace nodes
     *                               in the root of the tree.
     * @param {JqLite} elementsToRemove The jqLite element which we are going to replace. We keep
     *                                  the shell, but replace its DOM node reference.
     * @param {Node} newNode The new DOM node.
     */
    function replaceWith($rootElement, elementsToRemove, newNode) {
      var firstElementToRemove = elementsToRemove[0],
          removeCount = elementsToRemove.length,
          parent = firstElementToRemove.parentNode,
          i, ii;

      if ($rootElement) {
        for (i = 0, ii = $rootElement.length; i < ii; i++) {
          if ($rootElement[i] === firstElementToRemove) {
            $rootElement[i++] = newNode;
            for (var j = i, j2 = j + removeCount - 1,
                     jj = $rootElement.length;
                 j < jj; j++, j2++) {
              if (j2 < jj) {
                $rootElement[j] = $rootElement[j2];
              } else {
                delete $rootElement[j];
              }
            }
            $rootElement.length -= removeCount - 1;

            // If the replaced element is also the jQuery .context then replace it
            // .context is a deprecated jQuery api, so we should set it only when jQuery set it
            // http://api.jquery.com/context/
            if ($rootElement.context === firstElementToRemove) {
              $rootElement.context = newNode;
            }
            break;
          }
        }
      }

      if (parent) {
        parent.replaceChild(newNode, firstElementToRemove);
      }

      // Append all the `elementsToRemove` to a fragment. This will...
      // - remove them from the DOM
      // - allow them to still be traversed with .nextSibling
      // - allow a single fragment.qSA to fetch all elements being removed
      var fragment = window.document.createDocumentFragment();
      for (i = 0; i < removeCount; i++) {
        fragment.appendChild(elementsToRemove[i]);
      }

      if (jqLite.hasData(firstElementToRemove)) {
        // Copy over user data (that includes Angular's $scope etc.). Don't copy private
        // data here because there's no public interface in jQuery to do that and copying over
        // event listeners (which is the main use of private data) wouldn't work anyway.
        jqLite.data(newNode, jqLite.data(firstElementToRemove));

        // Remove $destroy event listeners from `firstElementToRemove`
        jqLite(firstElementToRemove).off('$destroy');
      }

      // Cleanup any data/listeners on the elements and children.
      // This includes invoking the $destroy event on any elements with listeners.
      jqLite.cleanData(fragment.querySelectorAll('*'));

      // Update the jqLite collection to only contain the `newNode`
      for (i = 1; i < removeCount; i++) {
        delete elementsToRemove[i];
      }
      elementsToRemove[0] = newNode;
      elementsToRemove.length = 1;
    }


    function cloneAndAnnotateFn(fn, annotation) {
      return extend(function() { return fn.apply(null, arguments); }, fn, annotation);
    }


    function invokeLinkFn(linkFn, scope, $element, attrs, controllers, transcludeFn) {
      try {
        linkFn(scope, $element, attrs, controllers, transcludeFn);
      } catch (e) {
        $exceptionHandler(e, startingTag($element));
      }
    }

    function strictBindingsCheck(attrName, directiveName) {
      if (strictComponentBindingsEnabled) {
        throw $compileMinErr('missingattr',
          'Attribute \'{0}\' of \'{1}\' is non-optional and must be set!',
          attrName, directiveName);
      }
    }

    // Set up $watches for isolate scope and controller bindings.
    function initializeDirectiveBindings(scope, attrs, destination, bindings, directive) {
      var removeWatchCollection = [];
      var initialChanges = {};
      var changes;

      forEach(bindings, function initializeBinding(definition, scopeName) {
        var attrName = definition.attrName,
        optional = definition.optional,
        mode = definition.mode, // @, =, <, or &
        lastValue,
        parentGet, parentSet, compare, removeWatch;

        switch (mode) {

          case '@':
            if (!optional && !hasOwnProperty.call(attrs, attrName)) {
              strictBindingsCheck(attrName, directive.name);
              destination[scopeName] = attrs[attrName] = undefined;

            }
            removeWatch = attrs.$observe(attrName, function(value) {
              if (isString(value) || isBoolean(value)) {
                var oldValue = destination[scopeName];
                recordChanges(scopeName, value, oldValue);
                destination[scopeName] = value;
              }
            });
            attrs.$$observers[attrName].$$scope = scope;
            lastValue = attrs[attrName];
            if (isString(lastValue)) {
              // If the attribute has been provided then we trigger an interpolation to ensure
              // the value is there for use in the link fn
              destination[scopeName] = $interpolate(lastValue)(scope);
            } else if (isBoolean(lastValue)) {
              // If the attributes is one of the BOOLEAN_ATTR then Angular will have converted
              // the value to boolean rather than a string, so we special case this situation
              destination[scopeName] = lastValue;
            }
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);
            removeWatchCollection.push(removeWatch);
            break;

          case '=':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              strictBindingsCheck(attrName, directive.name);
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            if (parentGet.literal) {
              compare = equals;
            } else {
              compare = simpleCompare;
            }
            parentSet = parentGet.assign || function() {
              // reset the change, or we will throw this exception on every $digest
              lastValue = destination[scopeName] = parentGet(scope);
              throw $compileMinErr('nonassign',
                  'Expression \'{0}\' in attribute \'{1}\' used with directive \'{2}\' is non-assignable!',
                  attrs[attrName], attrName, directive.name);
            };
            lastValue = destination[scopeName] = parentGet(scope);
            var parentValueWatch = function parentValueWatch(parentValue) {
              if (!compare(parentValue, destination[scopeName])) {
                // we are out of sync and need to copy
                if (!compare(parentValue, lastValue)) {
                  // parent changed and it has precedence
                  destination[scopeName] = parentValue;
                } else {
                  // if the parent can be assigned then do so
                  parentSet(scope, parentValue = destination[scopeName]);
                }
              }
              lastValue = parentValue;
              return lastValue;
            };
            parentValueWatch.$stateful = true;
            if (definition.collection) {
              removeWatch = scope.$watchCollection(attrs[attrName], parentValueWatch);
            } else {
              removeWatch = scope.$watch($parse(attrs[attrName], parentValueWatch), null, parentGet.literal);
            }
            removeWatchCollection.push(removeWatch);
            break;

          case '<':
            if (!hasOwnProperty.call(attrs, attrName)) {
              if (optional) break;
              strictBindingsCheck(attrName, directive.name);
              attrs[attrName] = undefined;
            }
            if (optional && !attrs[attrName]) break;

            parentGet = $parse(attrs[attrName]);
            var deepWatch = parentGet.literal;

            var initialValue = destination[scopeName] = parentGet(scope);
            initialChanges[scopeName] = new SimpleChange(_UNINITIALIZED_VALUE, destination[scopeName]);

            removeWatch = scope.$watch(parentGet, function parentValueWatchAction(newValue, oldValue) {
              if (oldValue === newValue) {
                if (oldValue === initialValue || (deepWatch && equals(oldValue, initialValue))) {
                  return;
                }
                oldValue = initialValue;
              }
              recordChanges(scopeName, newValue, oldValue);
              destination[scopeName] = newValue;
            }, deepWatch);

            removeWatchCollection.push(removeWatch);
            break;

          case '&':
            if (!optional && !hasOwnProperty.call(attrs, attrName)) {
              strictBindingsCheck(attrName, directive.name);
            }
            // Don't assign Object.prototype method to scope
            parentGet = attrs.hasOwnProperty(attrName) ? $parse(attrs[attrName]) : noop;

            // Don't assign noop to destination if expression is not valid
            if (parentGet === noop && optional) break;

            destination[scopeName] = function(locals) {
              return parentGet(scope, locals);
            };
            break;
        }
      });

      function recordChanges(key, currentValue, previousValue) {
        if (isFunction(destination.$onChanges) && !simpleCompare(currentValue, previousValue)) {
          // If we have not already scheduled the top level onChangesQueue handler then do so now
          if (!onChangesQueue) {
            scope.$$postDigest(flushOnChangesQueue);
            onChangesQueue = [];
          }
          // If we have not already queued a trigger of onChanges for this controller then do so now
          if (!changes) {
            changes = {};
            onChangesQueue.push(triggerOnChangesHook);
          }
          // If the has been a change on this property already then we need to reuse the previous value
          if (changes[key]) {
            previousValue = changes[key].previousValue;
          }
          // Store this change
          changes[key] = new SimpleChange(previousValue, currentValue);
        }
      }

      function triggerOnChangesHook() {
        destination.$onChanges(changes);
        // Now clear the changes so that we schedule onChanges when more changes arrive
        changes = undefined;
      }

      return {
        initialChanges: initialChanges,
        removeWatches: removeWatchCollection.length && function removeWatches() {
          for (var i = 0, ii = removeWatchCollection.length; i < ii; ++i) {
            removeWatchCollection[i]();
          }
        }
      };
    }
  }];
}

function SimpleChange(previous, current) {
  this.previousValue = previous;
  this.currentValue = current;
}
SimpleChange.prototype.isFirstChange = function() { return this.previousValue === _UNINITIALIZED_VALUE; };


var PREFIX_REGEXP = /^((?:x|data)[:\-_])/i;
var SPECIAL_CHARS_REGEXP = /[:\-_]+(.)/g;

/**
 * Converts all accepted directives format into proper directive name.
 * @param name Name to normalize
 */
function directiveNormalize(name) {
  return name
    .replace(PREFIX_REGEXP, '')
    .replace(SPECIAL_CHARS_REGEXP, fnCamelCaseReplace);
}

/**
 * @ngdoc type
 * @name $compile.directive.Attributes
 *
 * @description
 * A shared object between directive compile / linking functions which contains normalized DOM
 * element attributes. The values reflect current binding state `{{ }}`. The normalization is
 * needed since all of these are treated as equivalent in Angular:
 *
 * ```
 *    <span ng:bind="a" ng-bind="a" data-ng-bind="a" x-ng-bind="a">
 * ```
 */

/**
 * @ngdoc property
 * @name $compile.directive.Attributes#$attr
 *
 * @description
 * A map of DOM element attribute names to the normalized name. This is
 * needed to do reverse lookup from normalized name back to actual name.
 */


/**
 * @ngdoc method
 * @name $compile.directive.Attributes#$set
 * @kind function
 *
 * @description
 * Set DOM element attribute value.
 *
 *
 * @param {string} name Normalized element attribute name of the property to modify. The name is
 *          reverse-translated using the {@link ng.$compile.directive.Attributes#$attr $attr}
 *          property to the original name.
 * @param {string} value Value to set the attribute to. The value can be an interpolated string.
 */



/**
 * Closure compiler type information
 */

function nodesetLinkingFn(
  /* angular.Scope */ scope,
  /* NodeList */ nodeList,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function directiveLinkingFn(
  /* nodesetLinkingFn */ nodesetLinkingFn,
  /* angular.Scope */ scope,
  /* Node */ node,
  /* Element */ rootElement,
  /* function(Function) */ boundTranscludeFn
) {}

function tokenDifference(str1, str2) {
  var values = '',
      tokens1 = str1.split(/\s+/),
      tokens2 = str2.split(/\s+/);

  outer:
  for (var i = 0; i < tokens1.length; i++) {
    var token = tokens1[i];
    for (var j = 0; j < tokens2.length; j++) {
      if (token === tokens2[j]) continue outer;
    }
    values += (values.length > 0 ? ' ' : '') + token;
  }
  return values;
}

function removeComments(jqNodes) {
  jqNodes = jqLite(jqNodes);
  var i = jqNodes.length;

  if (i <= 1) {
    return jqNodes;
  }

  while (i--) {
    var node = jqNodes[i];
    if (node.nodeType === NODE_TYPE_COMMENT ||
       (node.nodeType === NODE_TYPE_TEXT && node.nodeValue.trim() === '')) {
         splice.call(jqNodes, i, 1);
    }
  }
  return jqNodes;
}

var $controllerMinErr = minErr('$controller');


var CNTRL_REG = /^(\S+)(\s+as\s+([\w$]+))?$/;
function identifierForController(controller, ident) {
  if (ident && isString(ident)) return ident;
  if (isString(controller)) {
    var match = CNTRL_REG.exec(controller);
    if (match) return match[3];
  }
}


/**
 * @ngdoc provider
 * @name $controllerProvider
 * @this
 *
 * @description
 * The {@link ng.$controller $controller service} is used by Angular to create new
 * controllers.
 *
 * This provider allows controller registration via the
 * {@link ng.$controllerProvider#register register} method.
 */
function $ControllerProvider() {
  var controllers = {},
      globals = false;

  /**
   * @ngdoc method
   * @name $controllerProvider#has
   * @param {string} name Controller name to check.
   */
  this.has = function(name) {
    return controllers.hasOwnProperty(name);
  };

  /**
   * @ngdoc method
   * @name $controllerProvider#register
   * @param {string|Object} name Controller name, or an object map of controllers where the keys are
   *    the names and the values are the constructors.
   * @param {Function|Array} constructor Controller constructor fn (optionally decorated with DI
   *    annotations in the array notation).
   */
  this.register = function(name, constructor) {
    assertNotHasOwnProperty(name, 'controller');
    if (isObject(name)) {
      extend(controllers, name);
    } else {
      controllers[name] = constructor;
    }
  };

  /**
   * @ngdoc method
   * @name $controllerProvider#allowGlobals
   * @description If called, allows `$controller` to find controller constructors on `window`
   *
   * @deprecated
   * sinceVersion="v1.3.0"
   * removeVersion="v1.7.0"
   * This method of finding controllers has been deprecated.
   */
  this.allowGlobals = function() {
    globals = true;
  };


  this.$get = ['$injector', '$window', function($injector, $window) {

    /**
     * @ngdoc service
     * @name $controller
     * @requires $injector
     *
     * @param {Function|string} constructor If called with a function then it's considered to be the
     *    controller constructor function. Otherwise it's considered to be a string which is used
     *    to retrieve the controller constructor using the following steps:
     *
     *    * check if a controller with given name is registered via `$controllerProvider`
     *    * check if evaluating the string on the current scope returns a constructor
     *    * if $controllerProvider#allowGlobals, check `window[constructor]` on the global
     *      `window` object (deprecated, not recommended)
     *
     *    The string can use the `controller as property` syntax, where the controller instance is published
     *    as the specified property on the `scope`; the `scope` must be injected into `locals` param for this
     *    to work correctly.
     *
     * @param {Object} locals Injection locals for Controller.
     * @return {Object} Instance of given controller.
     *
     * @description
     * `$controller` service is responsible for instantiating controllers.
     *
     * It's just a simple call to {@link auto.$injector $injector}, but extracted into
     * a service, so that one can override this service with [BC version](https://gist.github.com/1649788).
     */
    return function $controller(expression, locals, later, ident) {
      // PRIVATE API:
      //   param `later` --- indicates that the controller's constructor is invoked at a later time.
      //                     If true, $controller will allocate the object with the correct
      //                     prototype chain, but will not invoke the controller until a returned
      //                     callback is invoked.
      //   param `ident` --- An optional label which overrides the label parsed from the controller
      //                     expression, if any.
      var instance, match, constructor, identifier;
      later = later === true;
      if (ident && isString(ident)) {
        identifier = ident;
      }

      if (isString(expression)) {
        match = expression.match(CNTRL_REG);
        if (!match) {
          throw $controllerMinErr('ctrlfmt',
            'Badly formed controller string \'{0}\'. ' +
            'Must match `__name__ as __id__` or `__name__`.', expression);
        }
        constructor = match[1];
        identifier = identifier || match[3];
        expression = controllers.hasOwnProperty(constructor)
            ? controllers[constructor]
            : getter(locals.$scope, constructor, true) ||
                (globals ? getter($window, constructor, true) : undefined);

        if (!expression) {
          throw $controllerMinErr('ctrlreg',
            'The controller with the name \'{0}\' is not registered.', constructor);
        }

        assertArgFn(expression, constructor, true);
      }

      if (later) {
        // Instantiate controller later:
        // This machinery is used to create an instance of the object before calling the
        // controller's constructor itself.
        //
        // This allows properties to be added to the controller before the constructor is
        // invoked. Primarily, this is used for isolate scope bindings in $compile.
        //
        // This feature is not intended for use by applications, and is thus not documented
        // publicly.
        // Object creation: http://jsperf.com/create-constructor/2
        var controllerPrototype = (isArray(expression) ?
          expression[expression.length - 1] : expression).prototype;
        instance = Object.create(controllerPrototype || null);

        if (identifier) {
          addIdentifier(locals, identifier, instance, constructor || expression.name);
        }

        return extend(function $controllerInit() {
          var result = $injector.invoke(expression, instance, locals, constructor);
          if (result !== instance && (isObject(result) || isFunction(result))) {
            instance = result;
            if (identifier) {
              // If result changed, re-assign controllerAs value to scope.
              addIdentifier(locals, identifier, instance, constructor || expression.name);
            }
          }
          return instance;
        }, {
          instance: instance,
          identifier: identifier
        });
      }

      instance = $injector.instantiate(expression, locals, constructor);

      if (identifier) {
        addIdentifier(locals, identifier, instance, constructor || expression.name);
      }

      return instance;
    };

    function addIdentifier(locals, identifier, instance, name) {
      if (!(locals && isObject(locals.$scope))) {
        throw minErr('$controller')('noscp',
          'Cannot export controller \'{0}\' as \'{1}\'! No $scope object provided via `locals`.',
          name, identifier);
      }

      locals.$scope[identifier] = instance;
    }
  }];
}

/**
 * @ngdoc service
 * @name $document
 * @requires $window
 * @this
 *
 * @description
 * A {@link angular.element jQuery or jqLite} wrapper for the browser's `window.document` object.
 *
 * @example
   <example module="documentExample" name="document">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <p>$document title: <b ng-bind="title"></b></p>
         <p>window.document title: <b ng-bind="windowTitle"></b></p>
       </div>
     </file>
     <file name="script.js">
       angular.module('documentExample', [])
         .controller('ExampleController', ['$scope', '$document', function($scope, $document) {
           $scope.title = $document[0].title;
           $scope.windowTitle = angular.element(window.document)[0].title;
         }]);
     </file>
   </example>
 */
function $DocumentProvider() {
  this.$get = ['$window', function(window) {
    return jqLite(window.document);
  }];
}


/**
 * @private
 * @this
 * Listens for document visibility change and makes the current status accessible.
 */
function $$IsDocumentHiddenProvider() {
  this.$get = ['$document', '$rootScope', function($document, $rootScope) {
    var doc = $document[0];
    var hidden = doc && doc.hidden;

    $document.on('visibilitychange', changeListener);

    $rootScope.$on('$destroy', function() {
      $document.off('visibilitychange', changeListener);
    });

    function changeListener() {
      hidden = doc.hidden;
    }

    return function() {
      return hidden;
    };
  }];
}

/**
 * @ngdoc service
 * @name $exceptionHandler
 * @requires ng.$log
 * @this
 *
 * @description
 * Any uncaught exception in angular expressions is delegated to this service.
 * The default implementation simply delegates to `$log.error` which logs it into
 * the browser console.
 *
 * In unit tests, if `angular-mocks.js` is loaded, this service is overridden by
 * {@link ngMock.$exceptionHandler mock $exceptionHandler} which aids in testing.
 *
 * ## Example:
 *
 * The example below will overwrite the default `$exceptionHandler` in order to (a) log uncaught
 * errors to the backend for later inspection by the developers and (b) to use `$log.warn()` instead
 * of `$log.error()`.
 *
 * ```js
 *   angular.
 *     module('exceptionOverwrite', []).
 *     factory('$exceptionHandler', ['$log', 'logErrorsToBackend', function($log, logErrorsToBackend) {
 *       return function myExceptionHandler(exception, cause) {
 *         logErrorsToBackend(exception, cause);
 *         $log.warn(exception, cause);
 *       };
 *     }]);
 * ```
 *
 * <hr />
 * Note, that code executed in event-listeners (even those registered using jqLite's `on`/`bind`
 * methods) does not delegate exceptions to the {@link ng.$exceptionHandler $exceptionHandler}
 * (unless executed during a digest).
 *
 * If you wish, you can manually delegate exceptions, e.g.
 * `try { ... } catch(e) { $exceptionHandler(e); }`
 *
 * @param {Error} exception Exception associated with the error.
 * @param {string=} cause Optional information about the context in which
 *       the error was thrown.
 *
 */
function $ExceptionHandlerProvider() {
  this.$get = ['$log', function($log) {
    return function(exception, cause) {
      $log.error.apply($log, arguments);
    };
  }];
}

var $$ForceReflowProvider = /** @this */ function() {
  this.$get = ['$document', function($document) {
    return function(domNode) {
      //the line below will force the browser to perform a repaint so
      //that all the animated elements within the animation frame will
      //be properly updated and drawn on screen. This is required to
      //ensure that the preparation animation is properly flushed so that
      //the active state picks up from there. DO NOT REMOVE THIS LINE.
      //DO NOT OPTIMIZE THIS LINE. THE MINIFIER WILL REMOVE IT OTHERWISE WHICH
      //WILL RESULT IN AN UNPREDICTABLE BUG THAT IS VERY HARD TO TRACK DOWN AND
      //WILL TAKE YEARS AWAY FROM YOUR LIFE.
      if (domNode) {
        if (!domNode.nodeType && domNode instanceof jqLite) {
          domNode = domNode[0];
        }
      } else {
        domNode = $document[0].body;
      }
      return domNode.offsetWidth + 1;
    };
  }];
};

var APPLICATION_JSON = 'application/json';
var CONTENT_TYPE_APPLICATION_JSON = {'Content-Type': APPLICATION_JSON + ';charset=utf-8'};
var JSON_START = /^\[|^\{(?!\{)/;
var JSON_ENDS = {
  '[': /]$/,
  '{': /}$/
};
var JSON_PROTECTION_PREFIX = /^\)]\}',?\n/;
var $httpMinErr = minErr('$http');

function serializeValue(v) {
  if (isObject(v)) {
    return isDate(v) ? v.toISOString() : toJson(v);
  }
  return v;
}


/** @this */
function $HttpParamSerializerProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializer
   * @description
   *
   * Default {@link $http `$http`} params serializer that converts objects to strings
   * according to the following rules:
   *
   * * `{'foo': 'bar'}` results in `foo=bar`
   * * `{'foo': Date.now()}` results in `foo=2015-04-01T09%3A50%3A49.262Z` (`toISOString()` and encoded representation of a Date object)
   * * `{'foo': ['bar', 'baz']}` results in `foo=bar&foo=baz` (repeated key for each array element)
   * * `{'foo': {'bar':'baz'}}` results in `foo=%7B%22bar%22%3A%22baz%22%7D` (stringified and encoded representation of an object)
   *
   * Note that serializer will sort the request parameters alphabetically.
   * */

  this.$get = function() {
    return function ngParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      forEachSorted(params, function(value, key) {
        if (value === null || isUndefined(value) || isFunction(value)) return;
        if (isArray(value)) {
          forEach(value, function(v) {
            parts.push(encodeUriQuery(key)  + '=' + encodeUriQuery(serializeValue(v)));
          });
        } else {
          parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(serializeValue(value)));
        }
      });

      return parts.join('&');
    };
  };
}

/** @this */
function $HttpParamSerializerJQLikeProvider() {
  /**
   * @ngdoc service
   * @name $httpParamSerializerJQLike
   *
   * @description
   *
   * Alternative {@link $http `$http`} params serializer that follows
   * jQuery's [`param()`](http://api.jquery.com/jquery.param/) method logic.
   * The serializer will also sort the params alphabetically.
   *
   * To use it for serializing `$http` request parameters, set it as the `paramSerializer` property:
   *
   * ```js
   * $http({
   *   url: myUrl,
   *   method: 'GET',
   *   params: myParams,
   *   paramSerializer: '$httpParamSerializerJQLike'
   * });
   * ```
   *
   * It is also possible to set it as the default `paramSerializer` in the
   * {@link $httpProvider#defaults `$httpProvider`}.
   *
   * Additionally, you can inject the serializer and use it explicitly, for example to serialize
   * form data for submission:
   *
   * ```js
   * .controller(function($http, $httpParamSerializerJQLike) {
   *   //...
   *
   *   $http({
   *     url: myUrl,
   *     method: 'POST',
   *     data: $httpParamSerializerJQLike(myData),
   *     headers: {
   *       'Content-Type': 'application/x-www-form-urlencoded'
   *     }
   *   });
   *
   * });
   * ```
   *
   * */
  this.$get = function() {
    return function jQueryLikeParamSerializer(params) {
      if (!params) return '';
      var parts = [];
      serialize(params, '', true);
      return parts.join('&');

      function serialize(toSerialize, prefix, topLevel) {
        if (toSerialize === null || isUndefined(toSerialize)) return;
        if (isArray(toSerialize)) {
          forEach(toSerialize, function(value, index) {
            serialize(value, prefix + '[' + (isObject(value) ? index : '') + ']');
          });
        } else if (isObject(toSerialize) && !isDate(toSerialize)) {
          forEachSorted(toSerialize, function(value, key) {
            serialize(value, prefix +
                (topLevel ? '' : '[') +
                key +
                (topLevel ? '' : ']'));
          });
        } else {
          parts.push(encodeUriQuery(prefix) + '=' + encodeUriQuery(serializeValue(toSerialize)));
        }
      }
    };
  };
}

function defaultHttpResponseTransform(data, headers) {
  if (isString(data)) {
    // Strip json vulnerability protection prefix and trim whitespace
    var tempData = data.replace(JSON_PROTECTION_PREFIX, '').trim();

    if (tempData) {
      var contentType = headers('Content-Type');
      var hasJsonContentType = contentType && (contentType.indexOf(APPLICATION_JSON) === 0);

      if (hasJsonContentType || isJsonLike(tempData)) {
        try {
          data = fromJson(tempData);
        } catch (e) {
          if (!hasJsonContentType) {
            return data;
          }
          throw $httpMinErr('baddata', 'Data must be a valid JSON object. Received: "{0}". ' +
          'Parse error: "{1}"', data, e);
        }
      }
    }
  }

  return data;
}

function isJsonLike(str) {
    var jsonStart = str.match(JSON_START);
    return jsonStart && JSON_ENDS[jsonStart[0]].test(str);
}

/**
 * Parse headers into key value object
 *
 * @param {string} headers Raw headers as a string
 * @returns {Object} Parsed headers as key value object
 */
function parseHeaders(headers) {
  var parsed = createMap(), i;

  function fillInParsed(key, val) {
    if (key) {
      parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
    }
  }

  if (isString(headers)) {
    forEach(headers.split('\n'), function(line) {
      i = line.indexOf(':');
      fillInParsed(lowercase(trim(line.substr(0, i))), trim(line.substr(i + 1)));
    });
  } else if (isObject(headers)) {
    forEach(headers, function(headerVal, headerKey) {
      fillInParsed(lowercase(headerKey), trim(headerVal));
    });
  }

  return parsed;
}


/**
 * Returns a function that provides access to parsed headers.
 *
 * Headers are lazy parsed when first requested.
 * @see parseHeaders
 *
 * @param {(string|Object)} headers Headers to provide access to.
 * @returns {function(string=)} Returns a getter function which if called with:
 *
 *   - if called with an argument returns a single header value or null
 *   - if called with no arguments returns an object containing all headers.
 */
function headersGetter(headers) {
  var headersObj;

  return function(name) {
    if (!headersObj) headersObj =  parseHeaders(headers);

    if (name) {
      var value = headersObj[lowercase(name)];
      if (value === undefined) {
        value = null;
      }
      return value;
    }

    return headersObj;
  };
}


/**
 * Chain all given functions
 *
 * This function is used for both request and response transforming
 *
 * @param {*} data Data to transform.
 * @param {function(string=)} headers HTTP headers getter fn.
 * @param {number} status HTTP status code of the response.
 * @param {(Function|Array.<Function>)} fns Function or an array of functions.
 * @returns {*} Transformed data.
 */
function transformData(data, headers, status, fns) {
  if (isFunction(fns)) {
    return fns(data, headers, status);
  }

  forEach(fns, function(fn) {
    data = fn(data, headers, status);
  });

  return data;
}


function isSuccess(status) {
  return 200 <= status && status < 300;
}


/**
 * @ngdoc provider
 * @name $httpProvider
 * @this
 *
 * @description
 * Use `$httpProvider` to change the default behavior of the {@link ng.$http $http} service.
 * */
function $HttpProvider() {
  /**
   * @ngdoc property
   * @name $httpProvider#defaults
   * @description
   *
   * Object containing default values for all {@link ng.$http $http} requests.
   *
   * - **`defaults.cache`** - {boolean|Object} - A boolean value or object created with
   * {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of HTTP responses
   * by default. See {@link $http#caching $http Caching} for more information.
   *
   * - **`defaults.headers`** - {Object} - Default headers for all $http requests.
   * Refer to {@link ng.$http#setting-http-headers $http} for documentation on
   * setting default headers.
   *     - **`defaults.headers.common`**
   *     - **`defaults.headers.post`**
   *     - **`defaults.headers.put`**
   *     - **`defaults.headers.patch`**
   *
   * - **`defaults.jsonpCallbackParam`** - `{string}` - the name of the query parameter that passes the name of the
   * callback in a JSONP request. The value of this parameter will be replaced with the expression generated by the
   * {@link $jsonpCallbacks} service. Defaults to `'callback'`.
   *
   * - **`defaults.paramSerializer`** - `{string|function(Object<string,string>):string}` - A function
   *  used to the prepare string representation of request parameters (specified as an object).
   *  If specified as string, it is interpreted as a function registered with the {@link auto.$injector $injector}.
   *  Defaults to {@link ng.$httpParamSerializer $httpParamSerializer}.
   *
   * - **`defaults.transformRequest`** -
   * `{Array<function(data, headersGetter)>|function(data, headersGetter)}` -
   * An array of functions (or a single function) which are applied to the request data.
   * By default, this is an array with one request transformation function:
   *
   *   - If the `data` property of the request configuration object contains an object, serialize it
   *     into JSON format.
   *
   * - **`defaults.transformResponse`** -
   * `{Array<function(data, headersGetter, status)>|function(data, headersGetter, status)}` -
   * An array of functions (or a single function) which are applied to the response data. By default,
   * this is an array which applies one response transformation function that does two things:
   *
   *  - If XSRF prefix is detected, strip it
   *    (see {@link ng.$http#security-considerations Security Considerations in the $http docs}).
   *  - If the `Content-Type` is `application/json` or the response looks like JSON,
   *    deserialize it using a JSON parser.
   *
   * - **`defaults.xsrfCookieName`** - {string} - Name of cookie containing the XSRF token.
   * Defaults value is `'XSRF-TOKEN'`.
   *
   * - **`defaults.xsrfHeaderName`** - {string} - Name of HTTP header to populate with the
   * XSRF token. Defaults value is `'X-XSRF-TOKEN'`.
   *
   **/
  var defaults = this.defaults = {
    // transform incoming response data
    transformResponse: [defaultHttpResponseTransform],

    // transform outgoing request data
    transformRequest: [function(d) {
      return isObject(d) && !isFile(d) && !isBlob(d) && !isFormData(d) ? toJson(d) : d;
    }],

    // default headers
    headers: {
      common: {
        'Accept': 'application/json, text/plain, */*'
      },
      post:   shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      put:    shallowCopy(CONTENT_TYPE_APPLICATION_JSON),
      patch:  shallowCopy(CONTENT_TYPE_APPLICATION_JSON)
    },

    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',

    paramSerializer: '$httpParamSerializer',

    jsonpCallbackParam: 'callback'
  };

  var useApplyAsync = false;
  /**
   * @ngdoc method
   * @name $httpProvider#useApplyAsync
   * @description
   *
   * Configure $http service to combine processing of multiple http responses received at around
   * the same time via {@link ng.$rootScope.Scope#$applyAsync $rootScope.$applyAsync}. This can result in
   * significant performance improvement for bigger applications that make many HTTP requests
   * concurrently (common during application bootstrap).
   *
   * Defaults to false. If no value is specified, returns the current configured value.
   *
   * @param {boolean=} value If true, when requests are loaded, they will schedule a deferred
   *    "apply" on the next tick, giving time for subsequent requests in a roughly ~10ms window
   *    to load and share the same digest cycle.
   *
   * @returns {boolean|Object} If a value is specified, returns the $httpProvider for chaining.
   *    otherwise, returns the current configured value.
   **/
  this.useApplyAsync = function(value) {
    if (isDefined(value)) {
      useApplyAsync = !!value;
      return this;
    }
    return useApplyAsync;
  };

  /**
   * @ngdoc property
   * @name $httpProvider#interceptors
   * @description
   *
   * Array containing service factories for all synchronous or asynchronous {@link ng.$http $http}
   * pre-processing of request or postprocessing of responses.
   *
   * These service factories are ordered by request, i.e. they are applied in the same order as the
   * array, on request, but reverse order, on response.
   *
   * {@link ng.$http#interceptors Interceptors detailed info}
   **/
  var interceptorFactories = this.interceptors = [];

  this.$get = ['$browser', '$httpBackend', '$$cookieReader', '$cacheFactory', '$rootScope', '$q', '$injector', '$sce',
      function($browser, $httpBackend, $$cookieReader, $cacheFactory, $rootScope, $q, $injector, $sce) {

    var defaultCache = $cacheFactory('$http');

    /**
     * Make sure that default param serializer is exposed as a function
     */
    defaults.paramSerializer = isString(defaults.paramSerializer) ?
      $injector.get(defaults.paramSerializer) : defaults.paramSerializer;

    /**
     * Interceptors stored in reverse order. Inner interceptors before outer interceptors.
     * The reversal is needed so that we can build up the interception chain around the
     * server request.
     */
    var reversedInterceptors = [];

    forEach(interceptorFactories, function(interceptorFactory) {
      reversedInterceptors.unshift(isString(interceptorFactory)
          ? $injector.get(interceptorFactory) : $injector.invoke(interceptorFactory));
    });

    /**
     * @ngdoc service
     * @kind function
     * @name $http
     * @requires ng.$httpBackend
     * @requires $cacheFactory
     * @requires $rootScope
     * @requires $q
     * @requires $injector
     *
     * @description
     * The `$http` service is a core Angular service that facilitates communication with the remote
     * HTTP servers via the browser's [XMLHttpRequest](https://developer.mozilla.org/en/xmlhttprequest)
     * object or via [JSONP](http://en.wikipedia.org/wiki/JSONP).
     *
     * For unit testing applications that use `$http` service, see
     * {@link ngMock.$httpBackend $httpBackend mock}.
     *
     * For a higher level of abstraction, please check out the {@link ngResource.$resource
     * $resource} service.
     *
     * The $http API is based on the {@link ng.$q deferred/promise APIs} exposed by
     * the $q service. While for simple usage patterns this doesn't matter much, for advanced usage
     * it is important to familiarize yourself with these APIs and the guarantees they provide.
     *
     *
     * ## General usage
     * The `$http` service is a function which takes a single argument — a {@link $http#usage configuration object} —
     * that is used to generate an HTTP request and returns  a {@link ng.$q promise}.
     *
     * ```js
     *   // Simple GET request example:
     *   $http({
     *     method: 'GET',
     *     url: '/someUrl'
     *   }).then(function successCallback(response) {
     *       // this callback will be called asynchronously
     *       // when the response is available
     *     }, function errorCallback(response) {
     *       // called asynchronously if an error occurs
     *       // or server returns response with an error status.
     *     });
     * ```
     *
     * The response object has these properties:
     *
     *   - **data** – `{string|Object}` – The response body transformed with the transform
     *     functions.
     *   - **status** – `{number}` – HTTP status code of the response.
     *   - **headers** – `{function([headerName])}` – Header getter function.
     *   - **config** – `{Object}` – The configuration object that was used to generate the request.
     *   - **statusText** – `{string}` – HTTP status text of the response.
     *   - **xhrStatus** – `{string}` – Status of the XMLHttpRequest (`complete`, `error`, `timeout` or `abort`).
     *
     * A response status code between 200 and 299 is considered a success status and will result in
     * the success callback being called. Any response status code outside of that range is
     * considered an error status and will result in the error callback being called.
     * Also, status codes less than -1 are normalized to zero. -1 usually means the request was
     * aborted, e.g. using a `config.timeout`.
     * Note that if the response is a redirect, XMLHttpRequest will transparently follow it, meaning
     * that the outcome (success or error) will be determined by the final response status code.
     *
     *
     * ## Shortcut methods
     *
     * Shortcut methods are also available. All shortcut methods require passing in the URL, and
     * request data must be passed in for POST/PUT requests. An optional config can be passed as the
     * last argument.
     *
     * ```js
     *   $http.get('/someUrl', config).then(successCallback, errorCallback);
     *   $http.post('/someUrl', data, config).then(successCallback, errorCallback);
     * ```
     *
     * Complete list of shortcut methods:
     *
     * - {@link ng.$http#get $http.get}
     * - {@link ng.$http#head $http.head}
     * - {@link ng.$http#post $http.post}
     * - {@link ng.$http#put $http.put}
     * - {@link ng.$http#delete $http.delete}
     * - {@link ng.$http#jsonp $http.jsonp}
     * - {@link ng.$http#patch $http.patch}
     *
     *
     * ## Writing Unit Tests that use $http
     * When unit testing (using {@link ngMock ngMock}), it is necessary to call
     * {@link ngMock.$httpBackend#flush $httpBackend.flush()} to flush each pending
     * request using trained responses.
     *
     * ```
     * $httpBackend.expectGET(...);
     * $http.get(...);
     * $httpBackend.flush();
     * ```
     *
     * ## Setting HTTP Headers
     *
     * The $http service will automatically add certain HTTP headers to all requests. These defaults
     * can be fully configured by accessing the `$httpProvider.defaults.headers` configuration
     * object, which currently contains this default configuration:
     *
     * - `$httpProvider.defaults.headers.common` (headers that are common for all requests):
     *   - <code>Accept: application/json, text/plain, \*&#65279;/&#65279;\*</code>
     * - `$httpProvider.defaults.headers.post`: (header defaults for POST requests)
     *   - `Content-Type: application/json`
     * - `$httpProvider.defaults.headers.put` (header defaults for PUT requests)
     *   - `Content-Type: application/json`
     *
     * To add or overwrite these defaults, simply add or remove a property from these configuration
     * objects. To add headers for an HTTP method other than POST or PUT, simply add a new object
     * with the lowercased HTTP method name as the key, e.g.
     * `$httpProvider.defaults.headers.get = { 'My-Header' : 'value' }`.
     *
     * The defaults can also be set at runtime via the `$http.defaults` object in the same
     * fashion. For example:
     *
     * ```
     * module.run(function($http) {
     *   $http.defaults.headers.common.Authorization = 'Basic YmVlcDpib29w';
     * });
     * ```
     *
     * In addition, you can supply a `headers` property in the config object passed when
     * calling `$http(config)`, which overrides the defaults without changing them globally.
     *
     * To explicitly remove a header automatically added via $httpProvider.defaults.headers on a per request basis,
     * Use the `headers` property, setting the desired header to `undefined`. For example:
     *
     * ```js
     * var req = {
     *  method: 'POST',
     *  url: 'http://example.com',
     *  headers: {
     *    'Content-Type': undefined
     *  },
     *  data: { test: 'test' }
     * }
     *
     * $http(req).then(function(){...}, function(){...});
     * ```
     *
     * ## Transforming Requests and Responses
     *
     * Both requests and responses can be transformed using transformation functions: `transformRequest`
     * and `transformResponse`. These properties can be a single function that returns
     * the transformed value (`function(data, headersGetter, status)`) or an array of such transformation functions,
     * which allows you to `push` or `unshift` a new transformation function into the transformation chain.
     *
     * <div class="alert alert-warning">
     * **Note:** Angular does not make a copy of the `data` parameter before it is passed into the `transformRequest` pipeline.
     * That means changes to the properties of `data` are not local to the transform function (since Javascript passes objects by reference).
     * For example, when calling `$http.get(url, $scope.myObject)`, modifications to the object's properties in a transformRequest
     * function will be reflected on the scope and in any templates where the object is data-bound.
     * To prevent this, transform functions should have no side-effects.
     * If you need to modify properties, it is recommended to make a copy of the data, or create new object to return.
     * </div>
     *
     * ### Default Transformations
     *
     * The `$httpProvider` provider and `$http` service expose `defaults.transformRequest` and
     * `defaults.transformResponse` properties. If a request does not provide its own transformations
     * then these will be applied.
     *
     * You can augment or replace the default transformations by modifying these properties by adding to or
     * replacing the array.
     *
     * Angular provides the following default transformations:
     *
     * Request transformations (`$httpProvider.defaults.transformRequest` and `$http.defaults.transformRequest`) is
     * an array with one function that does the following:
     *
     * - If the `data` property of the request configuration object contains an object, serialize it
     *   into JSON format.
     *
     * Response transformations (`$httpProvider.defaults.transformResponse` and `$http.defaults.transformResponse`) is
     * an array with one function that does the following:
     *
     *  - If XSRF prefix is detected, strip it (see Security Considerations section below).
     *  - If the `Content-Type` is `application/json` or the response looks like JSON,
   *      deserialize it using a JSON parser.
     *
     *
     * ### Overriding the Default Transformations Per Request
     *
     * If you wish to override the request/response transformations only for a single request then provide
     * `transformRequest` and/or `transformResponse` properties on the configuration object passed
     * into `$http`.
     *
     * Note that if you provide these properties on the config object the default transformations will be
     * overwritten. If you wish to augment the default transformations then you must include them in your
     * local transformation array.
     *
     * The following code demonstrates adding a new response transformation to be run after the default response
     * transformations have been run.
     *
     * ```js
     * function appendTransform(defaults, transform) {
     *
     *   // We can't guarantee that the default transformation is an array
     *   defaults = angular.isArray(defaults) ? defaults : [defaults];
     *
     *   // Append the new transformation to the defaults
     *   return defaults.concat(transform);
     * }
     *
     * $http({
     *   url: '...',
     *   method: 'GET',
     *   transformResponse: appendTransform($http.defaults.transformResponse, function(value) {
     *     return doTransform(value);
     *   })
     * });
     * ```
     *
     *
     * ## Caching
     *
     * {@link ng.$http `$http`} responses are not cached by default. To enable caching, you must
     * set the config.cache value or the default cache value to TRUE or to a cache object (created
     * with {@link ng.$cacheFactory `$cacheFactory`}). If defined, the value of config.cache takes
     * precedence over the default cache value.
     *
     * In order to:
     *   * cache all responses - set the default cache value to TRUE or to a cache object
     *   * cache a specific response - set config.cache value to TRUE or to a cache object
     *
     * If caching is enabled, but neither the default cache nor config.cache are set to a cache object,
     * then the default `$cacheFactory("$http")` object is used.
     *
     * The default cache value can be set by updating the
     * {@link ng.$http#defaults `$http.defaults.cache`} property or the
     * {@link $httpProvider#defaults `$httpProvider.defaults.cache`} property.
     *
     * When caching is enabled, {@link ng.$http `$http`} stores the response from the server using
     * the relevant cache object. The next time the same request is made, the response is returned
     * from the cache without sending a request to the server.
     *
     * Take note that:
     *
     *   * Only GET and JSONP requests are cached.
     *   * The cache key is the request URL including search parameters; headers are not considered.
     *   * Cached responses are returned asynchronously, in the same way as responses from the server.
     *   * If multiple identical requests are made using the same cache, which is not yet populated,
     *     one request will be made to the server and remaining requests will return the same response.
     *   * A cache-control header on the response does not affect if or how responses are cached.
     *
     *
     * ## Interceptors
     *
     * Before you start creating interceptors, be sure to understand the
     * {@link ng.$q $q and deferred/promise APIs}.
     *
     * For purposes of global error handling, authentication, or any kind of synchronous or
     * asynchronous pre-processing of request or postprocessing of responses, it is desirable to be
     * able to intercept requests before they are handed to the server and
     * responses before they are handed over to the application code that
     * initiated these requests. The interceptors leverage the {@link ng.$q
     * promise APIs} to fulfill this need for both synchronous and asynchronous pre-processing.
     *
     * The interceptors are service factories that are registered with the `$httpProvider` by
     * adding them to the `$httpProvider.interceptors` array. The factory is called and
     * injected with dependencies (if specified) and returns the interceptor.
     *
     * There are two kinds of interceptors (and two kinds of rejection interceptors):
     *
     *   * `request`: interceptors get called with a http {@link $http#usage config} object. The function is free to
     *     modify the `config` object or create a new one. The function needs to return the `config`
     *     object directly, or a promise containing the `config` or a new `config` object.
     *   * `requestError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *   * `response`: interceptors get called with http `response` object. The function is free to
     *     modify the `response` object or create a new one. The function needs to return the `response`
     *     object directly, or as a promise containing the `response` or a new `response` object.
     *   * `responseError`: interceptor gets called when a previous interceptor threw an error or
     *     resolved with a rejection.
     *
     *
     * ```js
     *   // register the interceptor as a service
     *   $provide.factory('myHttpInterceptor', function($q, dependency1, dependency2) {
     *     return {
     *       // optional method
     *       'request': function(config) {
     *         // do something on success
     *         return config;
     *       },
     *
     *       // optional method
     *      'requestError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       },
     *
     *
     *
     *       // optional method
     *       'response': function(response) {
     *         // do something on success
     *         return response;
     *       },
     *
     *       // optional method
     *      'responseError': function(rejection) {
     *         // do something on error
     *         if (canRecover(rejection)) {
     *           return responseOrNewPromise
     *         }
     *         return $q.reject(rejection);
     *       }
     *     };
     *   });
     *
     *   $httpProvider.interceptors.push('myHttpInterceptor');
     *
     *
     *   // alternatively, register the interceptor via an anonymous factory
     *   $httpProvider.interceptors.push(function($q, dependency1, dependency2) {
     *     return {
     *      'request': function(config) {
     *          // same as above
     *       },
     *
     *       'response': function(response) {
     *          // same as above
     *       }
     *     };
     *   });
     * ```
     *
     * ## Security Considerations
     *
     * When designing web applications, consider security threats from:
     *
     * - [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * - [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery)
     *
     * Both server and the client must cooperate in order to eliminate these threats. Angular comes
     * pre-configured with strategies that address these issues, but for this to work backend server
     * cooperation is required.
     *
     * ### JSON Vulnerability Protection
     *
     * A [JSON vulnerability](http://haacked.com/archive/2008/11/20/anatomy-of-a-subtle-json-vulnerability.aspx)
     * allows third party website to turn your JSON resource URL into
     * [JSONP](http://en.wikipedia.org/wiki/JSONP) request under some conditions. To
     * counter this your server can prefix all JSON requests with following string `")]}',\n"`.
     * Angular will automatically strip the prefix before processing it as JSON.
     *
     * For example if your server needs to return:
     * ```js
     * ['one','two']
     * ```
     *
     * which is vulnerable to attack, your server can return:
     * ```js
     * )]}',
     * ['one','two']
     * ```
     *
     * Angular will strip the prefix, before processing the JSON.
     *
     *
     * ### Cross Site Request Forgery (XSRF) Protection
     *
     * [XSRF](http://en.wikipedia.org/wiki/Cross-site_request_forgery) is an attack technique by
     * which the attacker can trick an authenticated user into unknowingly executing actions on your
     * website. Angular provides a mechanism to counter XSRF. When performing XHR requests, the
     * $http service reads a token from a cookie (by default, `XSRF-TOKEN`) and sets it as an HTTP
     * header (`X-XSRF-TOKEN`). Since only JavaScript that runs on your domain could read the
     * cookie, your server can be assured that the XHR came from JavaScript running on your domain.
     * The header will not be set for cross-domain requests.
     *
     * To take advantage of this, your server needs to set a token in a JavaScript readable session
     * cookie called `XSRF-TOKEN` on the first HTTP GET request. On subsequent XHR requests the
     * server can verify that the cookie matches `X-XSRF-TOKEN` HTTP header, and therefore be sure
     * that only JavaScript running on your domain could have sent the request. The token must be
     * unique for each user and must be verifiable by the server (to prevent the JavaScript from
     * making up its own tokens). We recommend that the token is a digest of your site's
     * authentication cookie with a [salt](https://en.wikipedia.org/wiki/Salt_(cryptography&#41;)
     * for added security.
     *
     * The name of the headers can be specified using the xsrfHeaderName and xsrfCookieName
     * properties of either $httpProvider.defaults at config-time, $http.defaults at run-time,
     * or the per-request config object.
     *
     * In order to prevent collisions in environments where multiple Angular apps share the
     * same domain or subdomain, we recommend that each application uses unique cookie name.
     *
     * @param {object} config Object describing the request to be made and how it should be
     *    processed. The object has following properties:
     *
     *    - **method** – `{string}` – HTTP method (e.g. 'GET', 'POST', etc)
     *    - **url** – `{string|TrustedObject}` – Absolute or relative URL of the resource that is being requested;
     *      or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     *    - **params** – `{Object.<string|Object>}` – Map of strings or objects which will be serialized
     *      with the `paramSerializer` and appended as GET parameters.
     *    - **data** – `{string|Object}` – Data to be sent as the request message data.
     *    - **headers** – `{Object}` – Map of strings or functions which return strings representing
     *      HTTP headers to send to the server. If the return value of a function is null, the
     *      header will not be sent. Functions accept a config object as an argument.
     *    - **eventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest object.
     *      To bind events to the XMLHttpRequest upload object, use `uploadEventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **uploadEventHandlers** - `{Object}` - Event listeners to be bound to the XMLHttpRequest upload
     *      object. To bind events to the XMLHttpRequest object, use `eventHandlers`.
     *      The handler will be called in the context of a `$apply` block.
     *    - **xsrfHeaderName** – `{string}` – Name of HTTP header to populate with the XSRF token.
     *    - **xsrfCookieName** – `{string}` – Name of cookie containing the XSRF token.
     *    - **transformRequest** –
     *      `{function(data, headersGetter)|Array.<function(data, headersGetter)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      request body and headers and returns its transformed (typically serialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **transformResponse** –
     *      `{function(data, headersGetter, status)|Array.<function(data, headersGetter, status)>}` –
     *      transform function or an array of such functions. The transform function takes the http
     *      response body, headers and status and returns its transformed (typically deserialized) version.
     *      See {@link ng.$http#overriding-the-default-transformations-per-request
     *      Overriding the Default Transformations}
     *    - **paramSerializer** - `{string|function(Object<string,string>):string}` - A function used to
     *      prepare the string representation of request parameters (specified as an object).
     *      If specified as string, it is interpreted as function registered with the
     *      {@link $injector $injector}, which means you can create your own serializer
     *      by registering it as a {@link auto.$provide#service service}.
     *      The default serializer is the {@link $httpParamSerializer $httpParamSerializer};
     *      alternatively, you can use the {@link $httpParamSerializerJQLike $httpParamSerializerJQLike}
     *    - **cache** – `{boolean|Object}` – A boolean value or object created with
     *      {@link ng.$cacheFactory `$cacheFactory`} to enable or disable caching of the HTTP response.
     *      See {@link $http#caching $http Caching} for more information.
     *    - **timeout** – `{number|Promise}` – timeout in milliseconds, or {@link ng.$q promise}
     *      that should abort the request when resolved.
     *    - **withCredentials** - `{boolean}` - whether to set the `withCredentials` flag on the
     *      XHR object. See [requests with credentials](https://developer.mozilla.org/docs/Web/HTTP/Access_control_CORS#Requests_with_credentials)
     *      for more information.
     *    - **responseType** - `{string}` - see
     *      [XMLHttpRequest.responseType](https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest#xmlhttprequest-responsetype).
     *
     * @returns {HttpPromise} Returns a {@link ng.$q `Promise}` that will be resolved to a response object
     *                        when the request succeeds or fails.
     *
     *
     * @property {Array.<Object>} pendingRequests Array of config objects for currently pending
     *   requests. This is primarily meant to be used for debugging purposes.
     *
     *
     * @example
<example module="httpExample" name="http-service">
<file name="index.html">
  <div ng-controller="FetchController">
    <select ng-model="method" aria-label="Request method">
      <option>GET</option>
      <option>JSONP</option>
    </select>
    <input type="text" ng-model="url" size="80" aria-label="URL" />
    <button id="fetchbtn" ng-click="fetch()">fetch</button><br>
    <button id="samplegetbtn" ng-click="updateModel('GET', 'http-hello.html')">Sample GET</button>
    <button id="samplejsonpbtn"
      ng-click="updateModel('JSONP',
                    'https://angularjs.org/greet.php?name=Super%20Hero')">
      Sample JSONP
    </button>
    <button id="invalidjsonpbtn"
      ng-click="updateModel('JSONP', 'https://angularjs.org/doesntexist')">
        Invalid JSONP
      </button>
    <pre>http status code: {{status}}</pre>
    <pre>http response data: {{data}}</pre>
  </div>
</file>
<file name="script.js">
  angular.module('httpExample', [])
    .config(['$sceDelegateProvider', function($sceDelegateProvider) {
      // We must whitelist the JSONP endpoint that we are using to show that we trust it
      $sceDelegateProvider.resourceUrlWhitelist([
        'self',
        'https://angularjs.org/**'
      ]);
    }])
    .controller('FetchController', ['$scope', '$http', '$templateCache',
      function($scope, $http, $templateCache) {
        $scope.method = 'GET';
        $scope.url = 'http-hello.html';

        $scope.fetch = function() {
          $scope.code = null;
          $scope.response = null;

          $http({method: $scope.method, url: $scope.url, cache: $templateCache}).
            then(function(response) {
              $scope.status = response.status;
              $scope.data = response.data;
            }, function(response) {
              $scope.data = response.data || 'Request failed';
              $scope.status = response.status;
          });
        };

        $scope.updateModel = function(method, url) {
          $scope.method = method;
          $scope.url = url;
        };
      }]);
</file>
<file name="http-hello.html">
  Hello, $http!
</file>
<file name="protractor.js" type="protractor">
  var status = element(by.binding('status'));
  var data = element(by.binding('data'));
  var fetchBtn = element(by.id('fetchbtn'));
  var sampleGetBtn = element(by.id('samplegetbtn'));
  var invalidJsonpBtn = element(by.id('invalidjsonpbtn'));

  it('should make an xhr GET request', function() {
    sampleGetBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('200');
    expect(data.getText()).toMatch(/Hello, \$http!/);
  });

// Commented out due to flakes. See https://github.com/angular/angular.js/issues/9185
// it('should make a JSONP request to angularjs.org', function() {
//   var sampleJsonpBtn = element(by.id('samplejsonpbtn'));
//   sampleJsonpBtn.click();
//   fetchBtn.click();
//   expect(status.getText()).toMatch('200');
//   expect(data.getText()).toMatch(/Super Hero!/);
// });

  it('should make JSONP request to invalid URL and invoke the error handler',
      function() {
    invalidJsonpBtn.click();
    fetchBtn.click();
    expect(status.getText()).toMatch('0');
    expect(data.getText()).toMatch('Request failed');
  });
</file>
</example>
     */
    function $http(requestConfig) {

      if (!isObject(requestConfig)) {
        throw minErr('$http')('badreq', 'Http request configuration must be an object.  Received: {0}', requestConfig);
      }

      if (!isString($sce.valueOf(requestConfig.url))) {
        throw minErr('$http')('badreq', 'Http request configuration url must be a string or a $sce trusted object.  Received: {0}', requestConfig.url);
      }

      var config = extend({
        method: 'get',
        transformRequest: defaults.transformRequest,
        transformResponse: defaults.transformResponse,
        paramSerializer: defaults.paramSerializer,
        jsonpCallbackParam: defaults.jsonpCallbackParam
      }, requestConfig);

      config.headers = mergeHeaders(requestConfig);
      config.method = uppercase(config.method);
      config.paramSerializer = isString(config.paramSerializer) ?
          $injector.get(config.paramSerializer) : config.paramSerializer;

      $browser.$$incOutstandingRequestCount();

      var requestInterceptors = [];
      var responseInterceptors = [];
      var promise = $q.resolve(config);

      // apply interceptors
      forEach(reversedInterceptors, function(interceptor) {
        if (interceptor.request || interceptor.requestError) {
          requestInterceptors.unshift(interceptor.request, interceptor.requestError);
        }
        if (interceptor.response || interceptor.responseError) {
          responseInterceptors.push(interceptor.response, interceptor.responseError);
        }
      });

      promise = chainInterceptors(promise, requestInterceptors);
      promise = promise.then(serverRequest);
      promise = chainInterceptors(promise, responseInterceptors);
      promise = promise.finally(completeOutstandingRequest);

      return promise;


      function chainInterceptors(promise, interceptors) {
        for (var i = 0, ii = interceptors.length; i < ii;) {
          var thenFn = interceptors[i++];
          var rejectFn = interceptors[i++];

          promise = promise.then(thenFn, rejectFn);
        }

        interceptors.length = 0;

        return promise;
      }

      function completeOutstandingRequest() {
        $browser.$$completeOutstandingRequest(noop);
      }

      function executeHeaderFns(headers, config) {
        var headerContent, processedHeaders = {};

        forEach(headers, function(headerFn, header) {
          if (isFunction(headerFn)) {
            headerContent = headerFn(config);
            if (headerContent != null) {
              processedHeaders[header] = headerContent;
            }
          } else {
            processedHeaders[header] = headerFn;
          }
        });

        return processedHeaders;
      }

      function mergeHeaders(config) {
        var defHeaders = defaults.headers,
            reqHeaders = extend({}, config.headers),
            defHeaderName, lowercaseDefHeaderName, reqHeaderName;

        defHeaders = extend({}, defHeaders.common, defHeaders[lowercase(config.method)]);

        // using for-in instead of forEach to avoid unnecessary iteration after header has been found
        defaultHeadersIteration:
        for (defHeaderName in defHeaders) {
          lowercaseDefHeaderName = lowercase(defHeaderName);

          for (reqHeaderName in reqHeaders) {
            if (lowercase(reqHeaderName) === lowercaseDefHeaderName) {
              continue defaultHeadersIteration;
            }
          }

          reqHeaders[defHeaderName] = defHeaders[defHeaderName];
        }

        // execute if header value is a function for merged headers
        return executeHeaderFns(reqHeaders, shallowCopy(config));
      }

      function serverRequest(config) {
        var headers = config.headers;
        var reqData = transformData(config.data, headersGetter(headers), undefined, config.transformRequest);

        // strip content-type if data is undefined
        if (isUndefined(reqData)) {
          forEach(headers, function(value, header) {
            if (lowercase(header) === 'content-type') {
              delete headers[header];
            }
          });
        }

        if (isUndefined(config.withCredentials) && !isUndefined(defaults.withCredentials)) {
          config.withCredentials = defaults.withCredentials;
        }

        // send request
        return sendReq(config, reqData).then(transformResponse, transformResponse);
      }

      function transformResponse(response) {
        // make a copy since the response must be cacheable
        var resp = extend({}, response);
        resp.data = transformData(response.data, response.headers, response.status,
                                  config.transformResponse);
        return (isSuccess(response.status))
          ? resp
          : $q.reject(resp);
      }
    }

    $http.pendingRequests = [];

    /**
     * @ngdoc method
     * @name $http#get
     *
     * @description
     * Shortcut method to perform `GET` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#delete
     *
     * @description
     * Shortcut method to perform `DELETE` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#head
     *
     * @description
     * Shortcut method to perform `HEAD` request.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#jsonp
     *
     * @description
     * Shortcut method to perform `JSONP` request.
     *
     * Note that, since JSONP requests are sensitive because the response is given full access to the browser,
     * the url must be declared, via {@link $sce} as a trusted resource URL.
     * You can trust a URL by adding it to the whitelist via
     * {@link $sceDelegateProvider#resourceUrlWhitelist  `$sceDelegateProvider.resourceUrlWhitelist`} or
     * by explicitly trusting the URL via {@link $sce#trustAsResourceUrl `$sce.trustAsResourceUrl(url)`}.
     *
     * JSONP requests must specify a callback to be used in the response from the server. This callback
     * is passed as a query parameter in the request. You must specify the name of this parameter by
     * setting the `jsonpCallbackParam` property on the request config object.
     *
     * ```
     * $http.jsonp('some/trusted/url', {jsonpCallbackParam: 'callback'})
     * ```
     *
     * You can also specify a default callback parameter name in `$http.defaults.jsonpCallbackParam`.
     * Initially this is set to `'callback'`.
     *
     * <div class="alert alert-danger">
     * You can no longer use the `JSON_CALLBACK` string as a placeholder for specifying where the callback
     * parameter value should go.
     * </div>
     *
     * If you would like to customise where and how the callbacks are stored then try overriding
     * or decorating the {@link $jsonpCallbacks} service.
     *
     * @param {string|TrustedObject} url Absolute or relative URL of the resource that is being requested;
     *                                   or an object created by a call to `$sce.trustAsResourceUrl(url)`.
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */
    createShortMethods('get', 'delete', 'head', 'jsonp');

    /**
     * @ngdoc method
     * @name $http#post
     *
     * @description
     * Shortcut method to perform `POST` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

    /**
     * @ngdoc method
     * @name $http#put
     *
     * @description
     * Shortcut method to perform `PUT` request.
     *
     * @param {string} url Relative or absolute URL specifying the destination of the request
     * @param {*} data Request content
     * @param {Object=} config Optional configuration object
     * @returns {HttpPromise} Future object
     */

     /**
      * @ngdoc method
      * @name $http#patch
      *
      * @description
      * Shortcut method to perform `PATCH` request.
      *
      * @param {string} url Relative or absolute URL specifying the destination of the request
      * @param {*} data Request content
      * @param {Object=} config Optional configuration object
      * @returns {HttpPromise} Future object
      */
    createShortMethodsWithData('post', 'put', 'patch');

        /**
         * @ngdoc property
         * @name $http#defaults
         *
         * @description
         * Runtime equivalent of the `$httpProvider.defaults` property. Allows configuration of
         * default headers, withCredentials as well as request and response transformations.
         *
         * See "Setting HTTP Headers" and "Transforming Requests and Responses" sections above.
         */
    $http.defaults = defaults;


    return $http;


    function createShortMethods(names) {
      forEach(arguments, function(name) {
        $http[name] = function(url, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url
          }));
        };
      });
    }


    function createShortMethodsWithData(name) {
      forEach(arguments, function(name) {
        $http[name] = function(url, data, config) {
          return $http(extend({}, config || {}, {
            method: name,
            url: url,
            data: data
          }));
        };
      });
    }


    /**
     * Makes the request.
     *
     * !!! ACCESSES CLOSURE VARS:
     * $httpBackend, defaults, $log, $rootScope, defaultCache, $http.pendingRequests
     */
    function sendReq(config, reqData) {
      var deferred = $q.defer(),
          promise = deferred.promise,
          cache,
          cachedResp,
          reqHeaders = config.headers,
          isJsonp = lowercase(config.method) === 'jsonp',
          url = config.url;

      if (isJsonp) {
        // JSONP is a pretty sensitive operation where we're allowing a script to have full access to
        // our DOM and JS space.  So we require that the URL satisfies SCE.RESOURCE_URL.
        url = $sce.getTrustedResourceUrl(url);
      } else if (!isString(url)) {
        // If it is not a string then the URL must be a $sce trusted object
        url = $sce.valueOf(url);
      }

      url = buildUrl(url, config.paramSerializer(config.params));

      if (isJsonp) {
        // Check the url and add the JSONP callback placeholder
        url = sanitizeJsonpCallbackParam(url, config.jsonpCallbackParam);
      }

      $http.pendingRequests.push(config);
      promise.then(removePendingReq, removePendingReq);

      if ((config.cache || defaults.cache) && config.cache !== false &&
          (config.method === 'GET' || config.method === 'JSONP')) {
        cache = isObject(config.cache) ? config.cache
            : isObject(/** @type {?} */ (defaults).cache)
              ? /** @type {?} */ (defaults).cache
              : defaultCache;
      }

      if (cache) {
        cachedResp = cache.get(url);
        if (isDefined(cachedResp)) {
          if (isPromiseLike(cachedResp)) {
            // cached request has already been sent, but there is no response yet
            cachedResp.then(resolvePromiseWithResult, resolvePromiseWithResult);
          } else {
            // serving from cache
            if (isArray(cachedResp)) {
              resolvePromise(cachedResp[1], cachedResp[0], shallowCopy(cachedResp[2]), cachedResp[3], cachedResp[4]);
            } else {
              resolvePromise(cachedResp, 200, {}, 'OK', 'complete');
            }
          }
        } else {
          // put the promise for the non-transformed response into cache as a placeholder
          cache.put(url, promise);
        }
      }


      // if we won't have the response in cache, set the xsrf headers and
      // send the request to the backend
      if (isUndefined(cachedResp)) {
        var xsrfValue = urlIsSameOrigin(config.url)
            ? $$cookieReader()[config.xsrfCookieName || defaults.xsrfCookieName]
            : undefined;
        if (xsrfValue) {
          reqHeaders[(config.xsrfHeaderName || defaults.xsrfHeaderName)] = xsrfValue;
        }

        $httpBackend(config.method, url, reqData, done, reqHeaders, config.timeout,
            config.withCredentials, config.responseType,
            createApplyHandlers(config.eventHandlers),
            createApplyHandlers(config.uploadEventHandlers));
      }

      return promise;

      function createApplyHandlers(eventHandlers) {
        if (eventHandlers) {
          var applyHandlers = {};
          forEach(eventHandlers, function(eventHandler, key) {
            applyHandlers[key] = function(event) {
              if (useApplyAsync) {
                $rootScope.$applyAsync(callEventHandler);
              } else if ($rootScope.$$phase) {
                callEventHandler();
              } else {
                $rootScope.$apply(callEventHandler);
              }

              function callEventHandler() {
                eventHandler(event);
              }
            };
          });
          return applyHandlers;
        }
      }


      /**
       * Callback registered to $httpBackend():
       *  - caches the response if desired
       *  - resolves the raw $http promise
       *  - calls $apply
       */
      function done(status, response, headersString, statusText, xhrStatus) {
        if (cache) {
          if (isSuccess(status)) {
            cache.put(url, [status, response, parseHeaders(headersString), statusText, xhrStatus]);
          } else {
            // remove promise from the cache
            cache.remove(url);
          }
        }

        function resolveHttpPromise() {
          resolvePromise(response, status, headersString, statusText, xhrStatus);
        }

        if (useApplyAsync) {
          $rootScope.$applyAsync(resolveHttpPromise);
        } else {
          resolveHttpPromise();
          if (!$rootScope.$$phase) $rootScope.$apply();
        }
      }


      /**
       * Resolves the raw $http promise.
       */
      function resolvePromise(response, status, headers, statusText, xhrStatus) {
        //status: HTTP response status code, 0, -1 (aborted by timeout / promise)
        status = status >= -1 ? status : 0;

        (isSuccess(status) ? deferred.resolve : deferred.reject)({
          data: response,
          status: status,
          headers: headersGetter(headers),
          config: config,
          statusText: statusText,
          xhrStatus: xhrStatus
        });
      }

      function resolvePromiseWithResult(result) {
        resolvePromise(result.data, result.status, shallowCopy(result.headers()), result.statusText, result.xhrStatus);
      }

      function removePendingReq() {
        var idx = $http.pendingRequests.indexOf(config);
        if (idx !== -1) $http.pendingRequests.splice(idx, 1);
      }
    }


    function buildUrl(url, serializedParams) {
      if (serializedParams.length > 0) {
        url += ((url.indexOf('?') === -1) ? '?' : '&') + serializedParams;
      }
      return url;
    }

    function sanitizeJsonpCallbackParam(url, key) {
      if (/[&?][^=]+=JSON_CALLBACK/.test(url)) {
        // Throw if the url already contains a reference to JSON_CALLBACK
        throw $httpMinErr('badjsonp', 'Illegal use of JSON_CALLBACK in url, "{0}"', url);
      }

      var callbackParamRegex = new RegExp('[&?]' + key + '=');
      if (callbackParamRegex.test(url)) {
        // Throw if the callback param was already provided
        throw $httpMinErr('badjsonp', 'Illegal use of callback param, "{0}", in url, "{1}"', key, url);
      }

      // Add in the JSON_CALLBACK callback param value
      url += ((url.indexOf('?') === -1) ? '?' : '&') + key + '=JSON_CALLBACK';

      return url;
    }
  }];
}

/**
 * @ngdoc service
 * @name $xhrFactory
 * @this
 *
 * @description
 * Factory function used to create XMLHttpRequest objects.
 *
 * Replace or decorate this service to create your own custom XMLHttpRequest objects.
 *
 * ```
 * angular.module('myApp', [])
 * .factory('$xhrFactory', function() {
 *   return function createXhr(method, url) {
 *     return new window.XMLHttpRequest({mozSystem: true});
 *   };
 * });
 * ```
 *
 * @param {string} method HTTP method of the request (GET, POST, PUT, ..)
 * @param {string} url URL of the request.
 */
function $xhrFactoryProvider() {
  this.$get = function() {
    return function createXhr() {
      return new window.XMLHttpRequest();
    };
  };
}

/**
 * @ngdoc service
 * @name $httpBackend
 * @requires $jsonpCallbacks
 * @requires $document
 * @requires $xhrFactory
 * @this
 *
 * @description
 * HTTP backend used by the {@link ng.$http service} that delegates to
 * XMLHttpRequest object or JSONP and deals with browser incompatibilities.
 *
 * You should never need to use this service directly, instead use the higher-level abstractions:
 * {@link ng.$http $http} or {@link ngResource.$resource $resource}.
 *
 * During testing this implementation is swapped with {@link ngMock.$httpBackend mock
 * $httpBackend} which can be trained with responses.
 */
function $HttpBackendProvider() {
  this.$get = ['$browser', '$jsonpCallbacks', '$document', '$xhrFactory', function($browser, $jsonpCallbacks, $document, $xhrFactory) {
    return createHttpBackend($browser, $xhrFactory, $browser.defer, $jsonpCallbacks, $document[0]);
  }];
}

function createHttpBackend($browser, createXhr, $browserDefer, callbacks, rawDocument) {
  // TODO(vojta): fix the signature
  return function(method, url, post, callback, headers, timeout, withCredentials, responseType, eventHandlers, uploadEventHandlers) {
    url = url || $browser.url();

    if (lowercase(method) === 'jsonp') {
      var callbackPath = callbacks.createCallback(url);
      var jsonpDone = jsonpReq(url, callbackPath, function(status, text) {
        // jsonpReq only ever sets status to 200 (OK), 404 (ERROR) or -1 (WAITING)
        var response = (status === 200) && callbacks.getResponse(callbackPath);
        completeRequest(callback, status, response, '', text, 'complete');
        callbacks.removeCallback(callbackPath);
      });
    } else {

      var xhr = createXhr(method, url);

      xhr.open(method, url, true);
      forEach(headers, function(value, key) {
        if (isDefined(value)) {
            xhr.setRequestHeader(key, value);
        }
      });

      xhr.onload = function requestLoaded() {
        var statusText = xhr.statusText || '';

        // responseText is the old-school way of retrieving response (supported by IE9)
        // response/responseType properties were introduced in XHR Level2 spec (supported by IE10)
        var response = ('response' in xhr) ? xhr.response : xhr.responseText;

        // normalize IE9 bug (http://bugs.jquery.com/ticket/1450)
        var status = xhr.status === 1223 ? 204 : xhr.status;

        // fix status code when it is 0 (0 status is undocumented).
        // Occurs when accessing file resources or on Android 4.1 stock browser
        // while retrieving files from application cache.
        if (status === 0) {
          status = response ? 200 : urlResolve(url).protocol === 'file' ? 404 : 0;
        }

        completeRequest(callback,
            status,
            response,
            xhr.getAllResponseHeaders(),
            statusText,
            'complete');
      };

      var requestError = function() {
        // The response is always empty
        // See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
        completeRequest(callback, -1, null, null, '', 'error');
      };

      var requestAborted = function() {
        completeRequest(callback, -1, null, null, '', 'abort');
      };

      var requestTimeout = function() {
        // The response is always empty
        // See https://xhr.spec.whatwg.org/#request-error-steps and https://fetch.spec.whatwg.org/#concept-network-error
        completeRequest(callback, -1, null, null, '', 'timeout');
      };

      xhr.onerror = requestError;
      xhr.onabort = requestAborted;
      xhr.ontimeout = requestTimeout;

      forEach(eventHandlers, function(value, key) {
          xhr.addEventListener(key, value);
      });

      forEach(uploadEventHandlers, function(value, key) {
        xhr.upload.addEventListener(key, value);
      });

      if (withCredentials) {
        xhr.withCredentials = true;
      }

      if (responseType) {
        try {
          xhr.responseType = responseType;
        } catch (e) {
          // WebKit added support for the json responseType value on 09/03/2013
          // https://bugs.webkit.org/show_bug.cgi?id=73648. Versions of Safari prior to 7 are
          // known to throw when setting the value "json" as the response type. Other older
          // browsers implementing the responseType
          //
          // The json response type can be ignored if not supported, because JSON payloads are
          // parsed on the client-side regardless.
          if (responseType !== 'json') {
            throw e;
          }
        }
      }

      xhr.send(isUndefined(post) ? null : post);
    }

    if (timeout > 0) {
      var timeoutId = $browserDefer(timeoutRequest, timeout);
    } else if (isPromiseLike(timeout)) {
      timeout.then(timeoutRequest);
    }


    function timeoutRequest() {
      if (jsonpDone) {
        jsonpDone();
      }
      if (xhr) {
        xhr.abort();
      }
    }

    function completeRequest(callback, status, response, headersString, statusText, xhrStatus) {
      // cancel timeout and subsequent timeout promise resolution
      if (isDefined(timeoutId)) {
        $browserDefer.cancel(timeoutId);
      }
      jsonpDone = xhr = null;

      callback(status, response, headersString, statusText, xhrStatus);
    }
  };

  function jsonpReq(url, callbackPath, done) {
    url = url.replace('JSON_CALLBACK', callbackPath);
    // we can't use jQuery/jqLite here because jQuery does crazy stuff with script elements, e.g.:
    // - fetches local scripts via XHR and evals them
    // - adds and immediately removes script elements from the document
    var script = rawDocument.createElement('script'), callback = null;
    script.type = 'text/javascript';
    script.src = url;
    script.async = true;

    callback = function(event) {
      script.removeEventListener('load', callback);
      script.removeEventListener('error', callback);
      rawDocument.body.removeChild(script);
      script = null;
      var status = -1;
      var text = 'unknown';

      if (event) {
        if (event.type === 'load' && !callbacks.wasCalled(callbackPath)) {
          event = { type: 'error' };
        }
        text = event.type;
        status = event.type === 'error' ? 404 : 200;
      }

      if (done) {
        done(status, text);
      }
    };

    script.addEventListener('load', callback);
    script.addEventListener('error', callback);
    rawDocument.body.appendChild(script);
    return callback;
  }
}

var $interpolateMinErr = angular.$interpolateMinErr = minErr('$interpolate');
$interpolateMinErr.throwNoconcat = function(text) {
  throw $interpolateMinErr('noconcat',
      'Error while interpolating: {0}\nStrict Contextual Escaping disallows ' +
      'interpolations that concatenate multiple expressions when a trusted value is ' +
      'required.  See http://docs.angularjs.org/api/ng.$sce', text);
};

$interpolateMinErr.interr = function(text, err) {
  return $interpolateMinErr('interr', 'Can\'t interpolate: {0}\n{1}', text, err.toString());
};

/**
 * @ngdoc provider
 * @name $interpolateProvider
 * @this
 *
 * @description
 *
 * Used for configuring the interpolation markup. Defaults to `{{` and `}}`.
 *
 * <div class="alert alert-danger">
 * This feature is sometimes used to mix different markup languages, e.g. to wrap an Angular
 * template within a Python Jinja template (or any other template language). Mixing templating
 * languages is **very dangerous**. The embedding template language will not safely escape Angular
 * expressions, so any user-controlled values in the template will cause Cross Site Scripting (XSS)
 * security bugs!
 * </div>
 *
 * @example
<example name="custom-interpolation-markup" module="customInterpolationApp">
<file name="index.html">
<script>
  var customInterpolationApp = angular.module('customInterpolationApp', []);

  customInterpolationApp.config(function($interpolateProvider) {
    $interpolateProvider.startSymbol('//');
    $interpolateProvider.endSymbol('//');
  });


  customInterpolationApp.controller('DemoController', function() {
      this.label = "This binding is brought you by // interpolation symbols.";
  });
</script>
<div ng-controller="DemoController as demo">
    //demo.label//
</div>
</file>
<file name="protractor.js" type="protractor">
  it('should interpolate binding with custom symbols', function() {
    expect(element(by.binding('demo.label')).getText()).toBe('This binding is brought you by // interpolation symbols.');
  });
</file>
</example>
 */
function $InterpolateProvider() {
  var startSymbol = '{{';
  var endSymbol = '}}';

  /**
   * @ngdoc method
   * @name $interpolateProvider#startSymbol
   * @description
   * Symbol to denote start of expression in the interpolated string. Defaults to `{{`.
   *
   * @param {string=} value new value to set the starting symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.startSymbol = function(value) {
    if (value) {
      startSymbol = value;
      return this;
    } else {
      return startSymbol;
    }
  };

  /**
   * @ngdoc method
   * @name $interpolateProvider#endSymbol
   * @description
   * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
   *
   * @param {string=} value new value to set the ending symbol to.
   * @returns {string|self} Returns the symbol when used as getter and self if used as setter.
   */
  this.endSymbol = function(value) {
    if (value) {
      endSymbol = value;
      return this;
    } else {
      return endSymbol;
    }
  };


  this.$get = ['$parse', '$exceptionHandler', '$sce', function($parse, $exceptionHandler, $sce) {
    var startSymbolLength = startSymbol.length,
        endSymbolLength = endSymbol.length,
        escapedStartRegexp = new RegExp(startSymbol.replace(/./g, escape), 'g'),
        escapedEndRegexp = new RegExp(endSymbol.replace(/./g, escape), 'g');

    function escape(ch) {
      return '\\\\\\' + ch;
    }

    function unescapeText(text) {
      return text.replace(escapedStartRegexp, startSymbol).
        replace(escapedEndRegexp, endSymbol);
    }

    // TODO: this is the same as the constantWatchDelegate in parse.js
    function constantWatchDelegate(scope, listener, objectEquality, constantInterp) {
      var unwatch = scope.$watch(function constantInterpolateWatch(scope) {
        unwatch();
        return constantInterp(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    /**
     * @ngdoc service
     * @name $interpolate
     * @kind function
     *
     * @requires $parse
     * @requires $sce
     *
     * @description
     *
     * Compiles a string with markup into an interpolation function. This service is used by the
     * HTML {@link ng.$compile $compile} service for data binding. See
     * {@link ng.$interpolateProvider $interpolateProvider} for configuring the
     * interpolation markup.
     *
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var exp = $interpolate('Hello {{name | uppercase}}!');
     *   expect(exp({name:'Angular'})).toEqual('Hello ANGULAR!');
     * ```
     *
     * `$interpolate` takes an optional fourth argument, `allOrNothing`. If `allOrNothing` is
     * `true`, the interpolation function will return `undefined` unless all embedded expressions
     * evaluate to a value other than `undefined`.
     *
     * ```js
     *   var $interpolate = ...; // injected
     *   var context = {greeting: 'Hello', name: undefined };
     *
     *   // default "forgiving" mode
     *   var exp = $interpolate('{{greeting}} {{name}}!');
     *   expect(exp(context)).toEqual('Hello !');
     *
     *   // "allOrNothing" mode
     *   exp = $interpolate('{{greeting}} {{name}}!', false, null, true);
     *   expect(exp(context)).toBeUndefined();
     *   context.name = 'Angular';
     *   expect(exp(context)).toEqual('Hello Angular!');
     * ```
     *
     * `allOrNothing` is useful for interpolating URLs. `ngSrc` and `ngSrcset` use this behavior.
     *
     * #### Escaped Interpolation
     * $interpolate provides a mechanism for escaping interpolation markers. Start and end markers
     * can be escaped by preceding each of their characters with a REVERSE SOLIDUS U+005C (backslash).
     * It will be rendered as a regular start/end marker, and will not be interpreted as an expression
     * or binding.
     *
     * This enables web-servers to prevent script injection attacks and defacing attacks, to some
     * degree, while also enabling code examples to work without relying on the
     * {@link ng.directive:ngNonBindable ngNonBindable} directive.
     *
     * **For security purposes, it is strongly encouraged that web servers escape user-supplied data,
     * replacing angle brackets (&lt;, &gt;) with &amp;lt; and &amp;gt; respectively, and replacing all
     * interpolation start/end markers with their escaped counterparts.**
     *
     * Escaped interpolation markers are only replaced with the actual interpolation markers in rendered
     * output when the $interpolate service processes the text. So, for HTML elements interpolated
     * by {@link ng.$compile $compile}, or otherwise interpolated with the `mustHaveExpression` parameter
     * set to `true`, the interpolated text must contain an unescaped interpolation expression. As such,
     * this is typically useful only when user-data is used in rendering a template from the server, or
     * when otherwise untrusted data is used by a directive.
     *
     * <example name="interpolation">
     *  <file name="index.html">
     *    <div ng-init="username='A user'">
     *      <p ng-init="apptitle='Escaping demo'">{{apptitle}}: \{\{ username = "defaced value"; \}\}
     *        </p>
     *      <p><strong>{{username}}</strong> attempts to inject code which will deface the
     *        application, but fails to accomplish their task, because the server has correctly
     *        escaped the interpolation start/end markers with REVERSE SOLIDUS U+005C (backslash)
     *        characters.</p>
     *      <p>Instead, the result of the attempted script injection is visible, and can be removed
     *        from the database by an administrator.</p>
     *    </div>
     *  </file>
     * </example>
     *
     * @knownIssue
     * It is currently not possible for an interpolated expression to contain the interpolation end
     * symbol. For example, `{{ '}}' }}` will be incorrectly interpreted as `{{ ' }}` + `' }}`, i.e.
     * an interpolated expression consisting of a single-quote (`'`) and the `' }}` string.
     *
     * @knownIssue
     * All directives and components must use the standard `{{` `}}` interpolation symbols
     * in their templates. If you change the application interpolation symbols the {@link $compile}
     * service will attempt to denormalize the standard symbols to the custom symbols.
     * The denormalization process is not clever enough to know not to replace instances of the standard
     * symbols where they would not normally be treated as interpolation symbols. For example in the following
     * code snippet the closing braces of the literal object will get incorrectly denormalized:
     *
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"}}">
     * ```
     *
     * The workaround is to ensure that such instances are separated by whitespace:
     * ```
     * <div data-context='{"context":{"id":3,"type":"page"} }">
     * ```
     *
     * See https://github.com/angular/angular.js/pull/14610#issuecomment-219401099 for more information.
     *
     * @param {string} text The text with markup to interpolate.
     * @param {boolean=} mustHaveExpression if set to true then the interpolation string must have
     *    embedded expression in order to return an interpolation function. Strings with no
     *    embedded expression will return null for the interpolation function.
     * @param {string=} trustedContext when provided, the returned function passes the interpolated
     *    result through {@link ng.$sce#getTrusted $sce.getTrusted(interpolatedResult,
     *    trustedContext)} before returning it.  Refer to the {@link ng.$sce $sce} service that
     *    provides Strict Contextual Escaping for details.
     * @param {boolean=} allOrNothing if `true`, then the returned function returns undefined
     *    unless all embedded expressions evaluate to a value other than `undefined`.
     * @returns {function(context)} an interpolation function which is used to compute the
     *    interpolated string. The function has these parameters:
     *
     * - `context`: evaluation context for all expressions embedded in the interpolated text
     */
    function $interpolate(text, mustHaveExpression, trustedContext, allOrNothing) {
      // Provide a quick exit and simplified result function for text with no interpolation
      if (!text.length || text.indexOf(startSymbol) === -1) {
        var constantInterp;
        if (!mustHaveExpression) {
          var unescapedText = unescapeText(text);
          constantInterp = valueFn(unescapedText);
          constantInterp.exp = text;
          constantInterp.expressions = [];
          constantInterp.$$watchDelegate = constantWatchDelegate;
        }
        return constantInterp;
      }

      allOrNothing = !!allOrNothing;
      var startIndex,
          endIndex,
          index = 0,
          expressions = [],
          parseFns = [],
          textLength = text.length,
          exp,
          concat = [],
          expressionPositions = [];

      while (index < textLength) {
        if (((startIndex = text.indexOf(startSymbol, index)) !== -1) &&
             ((endIndex = text.indexOf(endSymbol, startIndex + startSymbolLength)) !== -1)) {
          if (index !== startIndex) {
            concat.push(unescapeText(text.substring(index, startIndex)));
          }
          exp = text.substring(startIndex + startSymbolLength, endIndex);
          expressions.push(exp);
          parseFns.push($parse(exp, parseStringifyInterceptor));
          index = endIndex + endSymbolLength;
          expressionPositions.push(concat.length);
          concat.push('');
        } else {
          // we did not find an interpolation, so we have to add the remainder to the separators array
          if (index !== textLength) {
            concat.push(unescapeText(text.substring(index)));
          }
          break;
        }
      }

      // Concatenating expressions makes it hard to reason about whether some combination of
      // concatenated values are unsafe to use and could easily lead to XSS.  By requiring that a
      // single expression be used for iframe[src], object[src], etc., we ensure that the value
      // that's used is assigned or constructed by some JS code somewhere that is more testable or
      // make it obvious that you bound the value to some user controlled value.  This helps reduce
      // the load when auditing for XSS issues.
      if (trustedContext && concat.length > 1) {
          $interpolateMinErr.throwNoconcat(text);
      }

      if (!mustHaveExpression || expressions.length) {
        var compute = function(values) {
          for (var i = 0, ii = expressions.length; i < ii; i++) {
            if (allOrNothing && isUndefined(values[i])) return;
            concat[expressionPositions[i]] = values[i];
          }
          return concat.join('');
        };

        var getValue = function(value) {
          return trustedContext ?
            $sce.getTrusted(trustedContext, value) :
            $sce.valueOf(value);
        };

        return extend(function interpolationFn(context) {
            var i = 0;
            var ii = expressions.length;
            var values = new Array(ii);

            try {
              for (; i < ii; i++) {
                values[i] = parseFns[i](context);
              }

              return compute(values);
            } catch (err) {
              $exceptionHandler($interpolateMinErr.interr(text, err));
            }

          }, {
          // all of these properties are undocumented for now
          exp: text, //just for compatibility with regular watchers created via $watch
          expressions: expressions,
          $$watchDelegate: function(scope, listener) {
            var lastValue;
            return scope.$watchGroup(parseFns, /** @this */ function interpolateFnWatcher(values, oldValues) {
              var currValue = compute(values);
              if (isFunction(listener)) {
                listener.call(this, currValue, values !== oldValues ? lastValue : currValue, scope);
              }
              lastValue = currValue;
            });
          }
        });
      }

      function parseStringifyInterceptor(value) {
        try {
          value = getValue(value);
          return allOrNothing && !isDefined(value) ? value : stringify(value);
        } catch (err) {
          $exceptionHandler($interpolateMinErr.interr(text, err));
        }
      }
    }


    /**
     * @ngdoc method
     * @name $interpolate#startSymbol
     * @description
     * Symbol to denote the start of expression in the interpolated string. Defaults to `{{`.
     *
     * Use {@link ng.$interpolateProvider#startSymbol `$interpolateProvider.startSymbol`} to change
     * the symbol.
     *
     * @returns {string} start symbol.
     */
    $interpolate.startSymbol = function() {
      return startSymbol;
    };


    /**
     * @ngdoc method
     * @name $interpolate#endSymbol
     * @description
     * Symbol to denote the end of expression in the interpolated string. Defaults to `}}`.
     *
     * Use {@link ng.$interpolateProvider#endSymbol `$interpolateProvider.endSymbol`} to change
     * the symbol.
     *
     * @returns {string} end symbol.
     */
    $interpolate.endSymbol = function() {
      return endSymbol;
    };

    return $interpolate;
  }];
}

/** @this */
function $IntervalProvider() {
  this.$get = ['$rootScope', '$window', '$q', '$$q', '$browser',
       function($rootScope,   $window,   $q,   $$q,   $browser) {
    var intervals = {};


     /**
      * @ngdoc service
      * @name $interval
      *
      * @description
      * Angular's wrapper for `window.setInterval`. The `fn` function is executed every `delay`
      * milliseconds.
      *
      * The return value of registering an interval function is a promise. This promise will be
      * notified upon each tick of the interval, and will be resolved after `count` iterations, or
      * run indefinitely if `count` is not defined. The value of the notification will be the
      * number of iterations that have run.
      * To cancel an interval, call `$interval.cancel(promise)`.
      *
      * In tests you can use {@link ngMock.$interval#flush `$interval.flush(millis)`} to
      * move forward by `millis` milliseconds and trigger any functions scheduled to run in that
      * time.
      *
      * <div class="alert alert-warning">
      * **Note**: Intervals created by this service must be explicitly destroyed when you are finished
      * with them.  In particular they are not automatically destroyed when a controller's scope or a
      * directive's element are destroyed.
      * You should take this into consideration and make sure to always cancel the interval at the
      * appropriate moment.  See the example below for more details on how and when to do this.
      * </div>
      *
      * @param {function()} fn A function that should be called repeatedly. If no additional arguments
      *   are passed (see below), the function is called with the current iteration count.
      * @param {number} delay Number of milliseconds between each function call.
      * @param {number=} [count=0] Number of times to repeat. If not set, or 0, will repeat
      *   indefinitely.
      * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
      *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
      * @param {...*=} Pass additional parameters to the executed function.
      * @returns {promise} A promise which will be notified on each iteration. It will resolve once all iterations of the interval complete.
      *
      * @example
      * <example module="intervalExample" name="interval-service">
      * <file name="index.html">
      *   <script>
      *     angular.module('intervalExample', [])
      *       .controller('ExampleController', ['$scope', '$interval',
      *         function($scope, $interval) {
      *           $scope.format = 'M/d/yy h:mm:ss a';
      *           $scope.blood_1 = 100;
      *           $scope.blood_2 = 120;
      *
      *           var stop;
      *           $scope.fight = function() {
      *             // Don't start a new fight if we are already fighting
      *             if ( angular.isDefined(stop) ) return;
      *
      *             stop = $interval(function() {
      *               if ($scope.blood_1 > 0 && $scope.blood_2 > 0) {
      *                 $scope.blood_1 = $scope.blood_1 - 3;
      *                 $scope.blood_2 = $scope.blood_2 - 4;
      *               } else {
      *                 $scope.stopFight();
      *               }
      *             }, 100);
      *           };
      *
      *           $scope.stopFight = function() {
      *             if (angular.isDefined(stop)) {
      *               $interval.cancel(stop);
      *               stop = undefined;
      *             }
      *           };
      *
      *           $scope.resetFight = function() {
      *             $scope.blood_1 = 100;
      *             $scope.blood_2 = 120;
      *           };
      *
      *           $scope.$on('$destroy', function() {
      *             // Make sure that the interval is destroyed too
      *             $scope.stopFight();
      *           });
      *         }])
      *       // Register the 'myCurrentTime' directive factory method.
      *       // We inject $interval and dateFilter service since the factory method is DI.
      *       .directive('myCurrentTime', ['$interval', 'dateFilter',
      *         function($interval, dateFilter) {
      *           // return the directive link function. (compile function not needed)
      *           return function(scope, element, attrs) {
      *             var format,  // date format
      *                 stopTime; // so that we can cancel the time updates
      *
      *             // used to update the UI
      *             function updateTime() {
      *               element.text(dateFilter(new Date(), format));
      *             }
      *
      *             // watch the expression, and update the UI on change.
      *             scope.$watch(attrs.myCurrentTime, function(value) {
      *               format = value;
      *               updateTime();
      *             });
      *
      *             stopTime = $interval(updateTime, 1000);
      *
      *             // listen on DOM destroy (removal) event, and cancel the next UI update
      *             // to prevent updating time after the DOM element was removed.
      *             element.on('$destroy', function() {
      *               $interval.cancel(stopTime);
      *             });
      *           }
      *         }]);
      *   </script>
      *
      *   <div>
      *     <div ng-controller="ExampleController">
      *       <label>Date format: <input ng-model="format"></label> <hr/>
      *       Current time is: <span my-current-time="format"></span>
      *       <hr/>
      *       Blood 1 : <font color='red'>{{blood_1}}</font>
      *       Blood 2 : <font color='red'>{{blood_2}}</font>
      *       <button type="button" data-ng-click="fight()">Fight</button>
      *       <button type="button" data-ng-click="stopFight()">StopFight</button>
      *       <button type="button" data-ng-click="resetFight()">resetFight</button>
      *     </div>
      *   </div>
      *
      * </file>
      * </example>
      */
    function interval(fn, delay, count, invokeApply) {
      var hasParams = arguments.length > 4,
          args = hasParams ? sliceArgs(arguments, 4) : [],
          setInterval = $window.setInterval,
          clearInterval = $window.clearInterval,
          iteration = 0,
          skipApply = (isDefined(invokeApply) && !invokeApply),
          deferred = (skipApply ? $$q : $q).defer(),
          promise = deferred.promise;

      count = isDefined(count) ? count : 0;

      promise.$$intervalId = setInterval(function tick() {
        if (skipApply) {
          $browser.defer(callback);
        } else {
          $rootScope.$evalAsync(callback);
        }
        deferred.notify(iteration++);

        if (count > 0 && iteration >= count) {
          deferred.resolve(iteration);
          clearInterval(promise.$$intervalId);
          delete intervals[promise.$$intervalId];
        }

        if (!skipApply) $rootScope.$apply();

      }, delay);

      intervals[promise.$$intervalId] = deferred;

      return promise;

      function callback() {
        if (!hasParams) {
          fn(iteration);
        } else {
          fn.apply(null, args);
        }
      }
    }


     /**
      * @ngdoc method
      * @name $interval#cancel
      *
      * @description
      * Cancels a task associated with the `promise`.
      *
      * @param {Promise=} promise returned by the `$interval` function.
      * @returns {boolean} Returns `true` if the task was successfully canceled.
      */
    interval.cancel = function(promise) {
      if (promise && promise.$$intervalId in intervals) {
        // Interval cancels should not report as unhandled promise.
        markQExceptionHandled(intervals[promise.$$intervalId].promise);
        intervals[promise.$$intervalId].reject('canceled');
        $window.clearInterval(promise.$$intervalId);
        delete intervals[promise.$$intervalId];
        return true;
      }
      return false;
    };

    return interval;
  }];
}

/**
 * @ngdoc service
 * @name $jsonpCallbacks
 * @requires $window
 * @description
 * This service handles the lifecycle of callbacks to handle JSONP requests.
 * Override this service if you wish to customise where the callbacks are stored and
 * how they vary compared to the requested url.
 */
var $jsonpCallbacksProvider = /** @this */ function() {
  this.$get = function() {
    var callbacks = angular.callbacks;
    var callbackMap = {};

    function createCallback(callbackId) {
      var callback = function(data) {
        callback.data = data;
        callback.called = true;
      };
      callback.id = callbackId;
      return callback;
    }

    return {
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#createCallback
       * @param {string} url the url of the JSONP request
       * @returns {string} the callback path to send to the server as part of the JSONP request
       * @description
       * {@link $httpBackend} calls this method to create a callback and get hold of the path to the callback
       * to pass to the server, which will be used to call the callback with its payload in the JSONP response.
       */
      createCallback: function(url) {
        var callbackId = '_' + (callbacks.$$counter++).toString(36);
        var callbackPath = 'angular.callbacks.' + callbackId;
        var callback = createCallback(callbackId);
        callbackMap[callbackPath] = callbacks[callbackId] = callback;
        return callbackPath;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#wasCalled
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {boolean} whether the callback has been called, as a result of the JSONP response
       * @description
       * {@link $httpBackend} calls this method to find out whether the JSONP response actually called the
       * callback that was passed in the request.
       */
      wasCalled: function(callbackPath) {
        return callbackMap[callbackPath].called;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#getResponse
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @returns {*} the data received from the response via the registered callback
       * @description
       * {@link $httpBackend} calls this method to get hold of the data that was provided to the callback
       * in the JSONP response.
       */
      getResponse: function(callbackPath) {
        return callbackMap[callbackPath].data;
      },
      /**
       * @ngdoc method
       * @name $jsonpCallbacks#removeCallback
       * @param {string} callbackPath the path to the callback that was sent in the JSONP request
       * @description
       * {@link $httpBackend} calls this method to remove the callback after the JSONP request has
       * completed or timed-out.
       */
      removeCallback: function(callbackPath) {
        var callback = callbackMap[callbackPath];
        delete callbacks[callback.id];
        delete callbackMap[callbackPath];
      }
    };
  };
};

/**
 * @ngdoc service
 * @name $locale
 *
 * @description
 * $locale service provides localization rules for various Angular components. As of right now the
 * only public api is:
 *
 * * `id` – `{string}` – locale id formatted as `languageId-countryId` (e.g. `en-us`)
 */

var PATH_MATCH = /^([^?#]*)(\?([^#]*))?(#(.*))?$/,
    DEFAULT_PORTS = {'http': 80, 'https': 443, 'ftp': 21};
var $locationMinErr = minErr('$location');


/**
 * Encode path using encodeUriSegment, ignoring forward slashes
 *
 * @param {string} path Path to encode
 * @returns {string}
 */
function encodePath(path) {
  var segments = path.split('/'),
      i = segments.length;

  while (i--) {
    segments[i] = encodeUriSegment(segments[i]);
  }

  return segments.join('/');
}

function parseAbsoluteUrl(absoluteUrl, locationObj) {
  var parsedUrl = urlResolve(absoluteUrl);

  locationObj.$$protocol = parsedUrl.protocol;
  locationObj.$$host = parsedUrl.hostname;
  locationObj.$$port = toInt(parsedUrl.port) || DEFAULT_PORTS[parsedUrl.protocol] || null;
}

var DOUBLE_SLASH_REGEX = /^\s*[\\/]{2,}/;
function parseAppUrl(url, locationObj) {

  if (DOUBLE_SLASH_REGEX.test(url)) {
    throw $locationMinErr('badpath', 'Invalid url "{0}".', url);
  }

  var prefixed = (url.charAt(0) !== '/');
  if (prefixed) {
    url = '/' + url;
  }
  var match = urlResolve(url);
  locationObj.$$path = decodeURIComponent(prefixed && match.pathname.charAt(0) === '/' ?
      match.pathname.substring(1) : match.pathname);
  locationObj.$$search = parseKeyValue(match.search);
  locationObj.$$hash = decodeURIComponent(match.hash);

  // make sure path starts with '/';
  if (locationObj.$$path && locationObj.$$path.charAt(0) !== '/') {
    locationObj.$$path = '/' + locationObj.$$path;
  }
}

function startsWith(str, search) {
  return str.slice(0, search.length) === search;
}

/**
 *
 * @param {string} base
 * @param {string} url
 * @returns {string} returns text from `url` after `base` or `undefined` if it does not begin with
 *                   the expected string.
 */
function stripBaseUrl(base, url) {
  if (startsWith(url, base)) {
    return url.substr(base.length);
  }
}


function stripHash(url) {
  var index = url.indexOf('#');
  return index === -1 ? url : url.substr(0, index);
}

function trimEmptyHash(url) {
  return url.replace(/(#.+)|#$/, '$1');
}


function stripFile(url) {
  return url.substr(0, stripHash(url).lastIndexOf('/') + 1);
}

/* return the server only (scheme://host:port) */
function serverBase(url) {
  return url.substring(0, url.indexOf('/', url.indexOf('//') + 2));
}


/**
 * LocationHtml5Url represents a URL
 * This object is exposed as $location service when HTML5 mode is enabled and supported
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} basePrefix URL path prefix
 */
function LocationHtml5Url(appBase, appBaseNoFile, basePrefix) {
  this.$$html5 = true;
  basePrefix = basePrefix || '';
  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given HTML5 (regular) URL string into properties
   * @param {string} url HTML5 URL
   * @private
   */
  this.$$parse = function(url) {
    var pathUrl = stripBaseUrl(appBaseNoFile, url);
    if (!isString(pathUrl)) {
      throw $locationMinErr('ipthprfx', 'Invalid url "{0}", missing path prefix "{1}".', url,
          appBaseNoFile);
    }

    parseAppUrl(pathUrl, this);

    if (!this.$$path) {
      this.$$path = '/';
    }

    this.$$compose();
  };

  /**
   * Compose url and update `absUrl` property
   * @private
   */
  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    this.$$absUrl = appBaseNoFile + this.$$url.substr(1); // first char is always '/'

    this.$$urlUpdatedByLocation = true;
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }
    var appUrl, prevAppUrl;
    var rewrittenUrl;


    if (isDefined(appUrl = stripBaseUrl(appBase, url))) {
      prevAppUrl = appUrl;
      if (basePrefix && isDefined(appUrl = stripBaseUrl(basePrefix, appUrl))) {
        rewrittenUrl = appBaseNoFile + (stripBaseUrl('/', appUrl) || appUrl);
      } else {
        rewrittenUrl = appBase + prevAppUrl;
      }
    } else if (isDefined(appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBaseNoFile + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when developer doesn't opt into html5 mode.
 * It also serves as the base class for html5 mode fallback on legacy browsers.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangUrl(appBase, appBaseNoFile, hashPrefix) {

  parseAbsoluteUrl(appBase, this);


  /**
   * Parse given hashbang URL into properties
   * @param {string} url Hashbang URL
   * @private
   */
  this.$$parse = function(url) {
    var withoutBaseUrl = stripBaseUrl(appBase, url) || stripBaseUrl(appBaseNoFile, url);
    var withoutHashUrl;

    if (!isUndefined(withoutBaseUrl) && withoutBaseUrl.charAt(0) === '#') {

      // The rest of the URL starts with a hash so we have
      // got either a hashbang path or a plain hash fragment
      withoutHashUrl = stripBaseUrl(hashPrefix, withoutBaseUrl);
      if (isUndefined(withoutHashUrl)) {
        // There was no hashbang prefix so we just have a hash fragment
        withoutHashUrl = withoutBaseUrl;
      }

    } else {
      // There was no hashbang path nor hash fragment:
      // If we are in HTML5 mode we use what is left as the path;
      // Otherwise we ignore what is left
      if (this.$$html5) {
        withoutHashUrl = withoutBaseUrl;
      } else {
        withoutHashUrl = '';
        if (isUndefined(withoutBaseUrl)) {
          appBase = url;
          /** @type {?} */ (this).replace();
        }
      }
    }

    parseAppUrl(withoutHashUrl, this);

    this.$$path = removeWindowsDriveName(this.$$path, withoutHashUrl, appBase);

    this.$$compose();

    /*
     * In Windows, on an anchor node on documents loaded from
     * the filesystem, the browser will return a pathname
     * prefixed with the drive name ('/C:/path') when a
     * pathname without a drive is set:
     *  * a.setAttribute('href', '/foo')
     *   * a.pathname === '/C:/foo' //true
     *
     * Inside of Angular, we're always using pathnames that
     * do not include drive names for routing.
     */
    function removeWindowsDriveName(path, url, base) {
      /*
      Matches paths for file protocol on windows,
      such as /C:/foo/bar, and captures only /foo/bar.
      */
      var windowsFilePathExp = /^\/[A-Z]:(\/.*)/;

      var firstPathSegmentMatch;

      //Get the relative path from the input URL.
      if (startsWith(url, base)) {
        url = url.replace(base, '');
      }

      // The input URL intentionally contains a first path segment that ends with a colon.
      if (windowsFilePathExp.exec(url)) {
        return path;
      }

      firstPathSegmentMatch = windowsFilePathExp.exec(path);
      return firstPathSegmentMatch ? firstPathSegmentMatch[1] : path;
    }
  };

  /**
   * Compose hashbang URL and update `absUrl` property
   * @private
   */
  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    this.$$absUrl = appBase + (this.$$url ? hashPrefix + this.$$url : '');

    this.$$urlUpdatedByLocation = true;
  };

  this.$$parseLinkUrl = function(url, relHref) {
    if (stripHash(appBase) === stripHash(url)) {
      this.$$parse(url);
      return true;
    }
    return false;
  };
}


/**
 * LocationHashbangUrl represents URL
 * This object is exposed as $location service when html5 history api is enabled but the browser
 * does not support it.
 *
 * @constructor
 * @param {string} appBase application base URL
 * @param {string} appBaseNoFile application base URL stripped of any filename
 * @param {string} hashPrefix hashbang prefix
 */
function LocationHashbangInHtml5Url(appBase, appBaseNoFile, hashPrefix) {
  this.$$html5 = true;
  LocationHashbangUrl.apply(this, arguments);

  this.$$parseLinkUrl = function(url, relHref) {
    if (relHref && relHref[0] === '#') {
      // special case for links to hash fragments:
      // keep the old url and only replace the hash fragment
      this.hash(relHref.slice(1));
      return true;
    }

    var rewrittenUrl;
    var appUrl;

    if (appBase === stripHash(url)) {
      rewrittenUrl = url;
    } else if ((appUrl = stripBaseUrl(appBaseNoFile, url))) {
      rewrittenUrl = appBase + hashPrefix + appUrl;
    } else if (appBaseNoFile === url + '/') {
      rewrittenUrl = appBaseNoFile;
    }
    if (rewrittenUrl) {
      this.$$parse(rewrittenUrl);
    }
    return !!rewrittenUrl;
  };

  this.$$compose = function() {
    var search = toKeyValue(this.$$search),
        hash = this.$$hash ? '#' + encodeUriSegment(this.$$hash) : '';

    this.$$url = encodePath(this.$$path) + (search ? '?' + search : '') + hash;
    // include hashPrefix in $$absUrl when $$url is empty so IE9 does not reload page because of removal of '#'
    this.$$absUrl = appBase + hashPrefix + this.$$url;

    this.$$urlUpdatedByLocation = true;
  };

}


var locationPrototype = {

  /**
   * Ensure absolute URL is initialized.
   * @private
   */
  $$absUrl:'',

  /**
   * Are we in html5 mode?
   * @private
   */
  $$html5: false,

  /**
   * Has any change been replacing?
   * @private
   */
  $$replace: false,

  /**
   * @ngdoc method
   * @name $location#absUrl
   *
   * @description
   * This method is getter only.
   *
   * Return full URL representation with all segments encoded according to rules specified in
   * [RFC 3986](http://www.ietf.org/rfc/rfc3986.txt).
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var absUrl = $location.absUrl();
   * // => "http://example.com/#/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @return {string} full URL
   */
  absUrl: locationGetter('$$absUrl'),

  /**
   * @ngdoc method
   * @name $location#url
   *
   * @description
   * This method is getter / setter.
   *
   * Return URL (e.g. `/path?a=b#hash`) when called without any parameter.
   *
   * Change path, search and hash, when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var url = $location.url();
   * // => "/some/path?foo=bar&baz=xoxo"
   * ```
   *
   * @param {string=} url New URL without base prefix (e.g. `/path?a=b#hash`)
   * @return {string} url
   */
  url: function(url) {
    if (isUndefined(url)) {
      return this.$$url;
    }

    var match = PATH_MATCH.exec(url);
    if (match[1] || url === '') this.path(decodeURIComponent(match[1]));
    if (match[2] || match[1] || url === '') this.search(match[3] || '');
    this.hash(match[5] || '');

    return this;
  },

  /**
   * @ngdoc method
   * @name $location#protocol
   *
   * @description
   * This method is getter only.
   *
   * Return protocol of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var protocol = $location.protocol();
   * // => "http"
   * ```
   *
   * @return {string} protocol of current URL
   */
  protocol: locationGetter('$$protocol'),

  /**
   * @ngdoc method
   * @name $location#host
   *
   * @description
   * This method is getter only.
   *
   * Return host of current URL.
   *
   * Note: compared to the non-angular version `location.host` which returns `hostname:port`, this returns the `hostname` portion only.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var host = $location.host();
   * // => "example.com"
   *
   * // given URL http://user:password@example.com:8080/#/some/path?foo=bar&baz=xoxo
   * host = $location.host();
   * // => "example.com"
   * host = location.host;
   * // => "example.com:8080"
   * ```
   *
   * @return {string} host of current URL.
   */
  host: locationGetter('$$host'),

  /**
   * @ngdoc method
   * @name $location#port
   *
   * @description
   * This method is getter only.
   *
   * Return port of current URL.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var port = $location.port();
   * // => 80
   * ```
   *
   * @return {Number} port
   */
  port: locationGetter('$$port'),

  /**
   * @ngdoc method
   * @name $location#path
   *
   * @description
   * This method is getter / setter.
   *
   * Return path of current URL when called without any parameter.
   *
   * Change path when called with parameter and return `$location`.
   *
   * Note: Path should always begin with forward slash (/), this method will add the forward slash
   * if it is missing.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var path = $location.path();
   * // => "/some/path"
   * ```
   *
   * @param {(string|number)=} path New path
   * @return {(string|object)} path if called with no parameters, or `$location` if called with a parameter
   */
  path: locationGetterSetter('$$path', function(path) {
    path = path !== null ? path.toString() : '';
    return path.charAt(0) === '/' ? path : '/' + path;
  }),

  /**
   * @ngdoc method
   * @name $location#search
   *
   * @description
   * This method is getter / setter.
   *
   * Return search part (as object) of current URL when called without any parameter.
   *
   * Change search part when called with parameter and return `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo
   * var searchObject = $location.search();
   * // => {foo: 'bar', baz: 'xoxo'}
   *
   * // set foo to 'yipee'
   * $location.search('foo', 'yipee');
   * // $location.search() => {foo: 'yipee', baz: 'xoxo'}
   * ```
   *
   * @param {string|Object.<string>|Object.<Array.<string>>} search New search params - string or
   * hash object.
   *
   * When called with a single argument the method acts as a setter, setting the `search` component
   * of `$location` to the specified value.
   *
   * If the argument is a hash object containing an array of values, these values will be encoded
   * as duplicate search parameters in the URL.
   *
   * @param {(string|Number|Array<string>|boolean)=} paramValue If `search` is a string or number, then `paramValue`
   * will override only a single search property.
   *
   * If `paramValue` is an array, it will override the property of the `search` component of
   * `$location` specified via the first argument.
   *
   * If `paramValue` is `null`, the property specified via the first argument will be deleted.
   *
   * If `paramValue` is `true`, the property specified via the first argument will be added with no
   * value nor trailing equal sign.
   *
   * @return {Object} If called with no arguments returns the parsed `search` object. If called with
   * one or more arguments returns `$location` object itself.
   */
  search: function(search, paramValue) {
    switch (arguments.length) {
      case 0:
        return this.$$search;
      case 1:
        if (isString(search) || isNumber(search)) {
          search = search.toString();
          this.$$search = parseKeyValue(search);
        } else if (isObject(search)) {
          search = copy(search, {});
          // remove object undefined or null properties
          forEach(search, function(value, key) {
            if (value == null) delete search[key];
          });

          this.$$search = search;
        } else {
          throw $locationMinErr('isrcharg',
              'The first argument of the `$location#search()` call must be a string or an object.');
        }
        break;
      default:
        if (isUndefined(paramValue) || paramValue === null) {
          delete this.$$search[search];
        } else {
          this.$$search[search] = paramValue;
        }
    }

    this.$$compose();
    return this;
  },

  /**
   * @ngdoc method
   * @name $location#hash
   *
   * @description
   * This method is getter / setter.
   *
   * Returns the hash fragment when called without any parameters.
   *
   * Changes the hash fragment when called with a parameter and returns `$location`.
   *
   *
   * ```js
   * // given URL http://example.com/#/some/path?foo=bar&baz=xoxo#hashValue
   * var hash = $location.hash();
   * // => "hashValue"
   * ```
   *
   * @param {(string|number)=} hash New hash fragment
   * @return {string} hash
   */
  hash: locationGetterSetter('$$hash', function(hash) {
    return hash !== null ? hash.toString() : '';
  }),

  /**
   * @ngdoc method
   * @name $location#replace
   *
   * @description
   * If called, all changes to $location during the current `$digest` will replace the current history
   * record, instead of adding a new one.
   */
  replace: function() {
    this.$$replace = true;
    return this;
  }
};

forEach([LocationHashbangInHtml5Url, LocationHashbangUrl, LocationHtml5Url], function(Location) {
  Location.prototype = Object.create(locationPrototype);

  /**
   * @ngdoc method
   * @name $location#state
   *
   * @description
   * This method is getter / setter.
   *
   * Return the history state object when called without any parameter.
   *
   * Change the history state object when called with one parameter and return `$location`.
   * The state object is later passed to `pushState` or `replaceState`.
   *
   * NOTE: This method is supported only in HTML5 mode and only in browsers supporting
   * the HTML5 History API (i.e. methods `pushState` and `replaceState`). If you need to support
   * older browsers (like IE9 or Android < 4.0), don't use this method.
   *
   * @param {object=} state State object for pushState or replaceState
   * @return {object} state
   */
  Location.prototype.state = function(state) {
    if (!arguments.length) {
      return this.$$state;
    }

    if (Location !== LocationHtml5Url || !this.$$html5) {
      throw $locationMinErr('nostate', 'History API state support is available only ' +
        'in HTML5 mode and only in browsers supporting HTML5 History API');
    }
    // The user might modify `stateObject` after invoking `$location.state(stateObject)`
    // but we're changing the $$state reference to $browser.state() during the $digest
    // so the modification window is narrow.
    this.$$state = isUndefined(state) ? null : state;
    this.$$urlUpdatedByLocation = true;

    return this;
  };
});


function locationGetter(property) {
  return /** @this */ function() {
    return this[property];
  };
}


function locationGetterSetter(property, preprocess) {
  return /** @this */ function(value) {
    if (isUndefined(value)) {
      return this[property];
    }

    this[property] = preprocess(value);
    this.$$compose();

    return this;
  };
}


/**
 * @ngdoc service
 * @name $location
 *
 * @requires $rootElement
 *
 * @description
 * The $location service parses the URL in the browser address bar (based on the
 * [window.location](https://developer.mozilla.org/en/window.location)) and makes the URL
 * available to your application. Changes to the URL in the address bar are reflected into
 * $location service and changes to $location are reflected into the browser address bar.
 *
 * **The $location service:**
 *
 * - Exposes the current URL in the browser address bar, so you can
 *   - Watch and observe the URL.
 *   - Change the URL.
 * - Synchronizes the URL with the browser when the user
 *   - Changes the address bar.
 *   - Clicks the back or forward button (or clicks a History link).
 *   - Clicks on a link.
 * - Represents the URL object as a set of methods (protocol, host, port, path, search, hash).
 *
 * For more information see {@link guide/$location Developer Guide: Using $location}
 */

/**
 * @ngdoc provider
 * @name $locationProvider
 * @this
 *
 * @description
 * Use the `$locationProvider` to configure how the application deep linking paths are stored.
 */
function $LocationProvider() {
  var hashPrefix = '!',
      html5Mode = {
        enabled: false,
        requireBase: true,
        rewriteLinks: true
      };

  /**
   * @ngdoc method
   * @name $locationProvider#hashPrefix
   * @description
   * The default value for the prefix is `'!'`.
   * @param {string=} prefix Prefix for hash part (containing path and search)
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.hashPrefix = function(prefix) {
    if (isDefined(prefix)) {
      hashPrefix = prefix;
      return this;
    } else {
      return hashPrefix;
    }
  };

  /**
   * @ngdoc method
   * @name $locationProvider#html5Mode
   * @description
   * @param {(boolean|Object)=} mode If boolean, sets `html5Mode.enabled` to value.
   *   If object, sets `enabled`, `requireBase` and `rewriteLinks` to respective values. Supported
   *   properties:
   *   - **enabled** – `{boolean}` – (default: false) If true, will rely on `history.pushState` to
   *     change urls where supported. Will fall back to hash-prefixed paths in browsers that do not
   *     support `pushState`.
   *   - **requireBase** - `{boolean}` - (default: `true`) When html5Mode is enabled, specifies
   *     whether or not a <base> tag is required to be present. If `enabled` and `requireBase` are
   *     true, and a base tag is not present, an error will be thrown when `$location` is injected.
   *     See the {@link guide/$location $location guide for more information}
   *   - **rewriteLinks** - `{boolean|string}` - (default: `true`) When html5Mode is enabled,
   *     enables/disables URL rewriting for relative links. If set to a string, URL rewriting will
   *     only happen on links with an attribute that matches the given string. For example, if set
   *     to `'internal-link'`, then the URL will only be rewritten for `<a internal-link>` links.
   *     Note that [attribute name normalization](guide/directive#normalization) does not apply
   *     here, so `'internalLink'` will **not** match `'internal-link'`.
   *
   * @returns {Object} html5Mode object if used as getter or itself (chaining) if used as setter
   */
  this.html5Mode = function(mode) {
    if (isBoolean(mode)) {
      html5Mode.enabled = mode;
      return this;
    } else if (isObject(mode)) {

      if (isBoolean(mode.enabled)) {
        html5Mode.enabled = mode.enabled;
      }

      if (isBoolean(mode.requireBase)) {
        html5Mode.requireBase = mode.requireBase;
      }

      if (isBoolean(mode.rewriteLinks) || isString(mode.rewriteLinks)) {
        html5Mode.rewriteLinks = mode.rewriteLinks;
      }

      return this;
    } else {
      return html5Mode;
    }
  };

  /**
   * @ngdoc event
   * @name $location#$locationChangeStart
   * @eventType broadcast on root scope
   * @description
   * Broadcasted before a URL will change.
   *
   * This change can be prevented by calling
   * `preventDefault` method of the event. See {@link ng.$rootScope.Scope#$on} for more
   * details about event object. Upon successful change
   * {@link ng.$location#$locationChangeSuccess $locationChangeSuccess} is fired.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  /**
   * @ngdoc event
   * @name $location#$locationChangeSuccess
   * @eventType broadcast on root scope
   * @description
   * Broadcasted after a URL was changed.
   *
   * The `newState` and `oldState` parameters may be defined only in HTML5 mode and when
   * the browser supports the HTML5 History API.
   *
   * @param {Object} angularEvent Synthetic event object.
   * @param {string} newUrl New URL
   * @param {string=} oldUrl URL that was before it was changed.
   * @param {string=} newState New history state object
   * @param {string=} oldState History state object that was before it was changed.
   */

  this.$get = ['$rootScope', '$browser', '$sniffer', '$rootElement', '$window',
      function($rootScope, $browser, $sniffer, $rootElement, $window) {
    var $location,
        LocationMode,
        baseHref = $browser.baseHref(), // if base[href] is undefined, it defaults to ''
        initialUrl = $browser.url(),
        appBase;

    if (html5Mode.enabled) {
      if (!baseHref && html5Mode.requireBase) {
        throw $locationMinErr('nobase',
          '$location in HTML5 mode requires a <base> tag to be present!');
      }
      appBase = serverBase(initialUrl) + (baseHref || '/');
      LocationMode = $sniffer.history ? LocationHtml5Url : LocationHashbangInHtml5Url;
    } else {
      appBase = stripHash(initialUrl);
      LocationMode = LocationHashbangUrl;
    }
    var appBaseNoFile = stripFile(appBase);

    $location = new LocationMode(appBase, appBaseNoFile, '#' + hashPrefix);
    $location.$$parseLinkUrl(initialUrl, initialUrl);

    $location.$$state = $browser.state();

    var IGNORE_URI_REGEXP = /^\s*(javascript|mailto):/i;

    function setBrowserUrlWithFallback(url, replace, state) {
      var oldUrl = $location.url();
      var oldState = $location.$$state;
      try {
        $browser.url(url, replace, state);

        // Make sure $location.state() returns referentially identical (not just deeply equal)
        // state object; this makes possible quick checking if the state changed in the digest
        // loop. Checking deep equality would be too expensive.
        $location.$$state = $browser.state();
      } catch (e) {
        // Restore old values if pushState fails
        $location.url(oldUrl);
        $location.$$state = oldState;

        throw e;
      }
    }

    $rootElement.on('click', function(event) {
      var rewriteLinks = html5Mode.rewriteLinks;
      // TODO(vojta): rewrite link when opening in new tab/window (in legacy browser)
      // currently we open nice url link and redirect then

      if (!rewriteLinks || event.ctrlKey || event.metaKey || event.shiftKey || event.which === 2 || event.button === 2) return;

      var elm = jqLite(event.target);

      // traverse the DOM up to find first A tag
      while (nodeName_(elm[0]) !== 'a') {
        // ignore rewriting if no A tag (reached root element, or no parent - removed from document)
        if (elm[0] === $rootElement[0] || !(elm = elm.parent())[0]) return;
      }

      if (isString(rewriteLinks) && isUndefined(elm.attr(rewriteLinks))) return;

      var absHref = elm.prop('href');
      // get the actual href attribute - see
      // http://msdn.microsoft.com/en-us/library/ie/dd347148(v=vs.85).aspx
      var relHref = elm.attr('href') || elm.attr('xlink:href');

      if (isObject(absHref) && absHref.toString() === '[object SVGAnimatedString]') {
        // SVGAnimatedString.animVal should be identical to SVGAnimatedString.baseVal, unless during
        // an animation.
        absHref = urlResolve(absHref.animVal).href;
      }

      // Ignore when url is started with javascript: or mailto:
      if (IGNORE_URI_REGEXP.test(absHref)) return;

      if (absHref && !elm.attr('target') && !event.isDefaultPrevented()) {
        if ($location.$$parseLinkUrl(absHref, relHref)) {
          // We do a preventDefault for all urls that are part of the angular application,
          // in html5mode and also without, so that we are able to abort navigation without
          // getting double entries in the location history.
          event.preventDefault();
          // update location manually
          if ($location.absUrl() !== $browser.url()) {
            $rootScope.$apply();
            // hack to work around FF6 bug 684208 when scenario runner clicks on links
            $window.angular['ff-684208-preventDefault'] = true;
          }
        }
      }
    });


    // rewrite hashbang url <> html5 url
    if (trimEmptyHash($location.absUrl()) !== trimEmptyHash(initialUrl)) {
      $browser.url($location.absUrl(), true);
    }

    var initializing = true;

    // update $location when $browser url changes
    $browser.onUrlChange(function(newUrl, newState) {

      if (!startsWith(newUrl, appBaseNoFile)) {
        // If we are navigating outside of the app then force a reload
        $window.location.href = newUrl;
        return;
      }

      $rootScope.$evalAsync(function() {
        var oldUrl = $location.absUrl();
        var oldState = $location.$$state;
        var defaultPrevented;
        newUrl = trimEmptyHash(newUrl);
        $location.$$parse(newUrl);
        $location.$$state = newState;

        defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
            newState, oldState).defaultPrevented;

        // if the location was changed by a `$locationChangeStart` handler then stop
        // processing this location change
        if ($location.absUrl() !== newUrl) return;

        if (defaultPrevented) {
          $location.$$parse(oldUrl);
          $location.$$state = oldState;
          setBrowserUrlWithFallback(oldUrl, false, oldState);
        } else {
          initializing = false;
          afterLocationChange(oldUrl, oldState);
        }
      });
      if (!$rootScope.$$phase) $rootScope.$digest();
    });

    // update browser
    $rootScope.$watch(function $locationWatch() {
      if (initializing || $location.$$urlUpdatedByLocation) {
        $location.$$urlUpdatedByLocation = false;

        var oldUrl = trimEmptyHash($browser.url());
        var newUrl = trimEmptyHash($location.absUrl());
        var oldState = $browser.state();
        var currentReplace = $location.$$replace;
        var urlOrStateChanged = oldUrl !== newUrl ||
          ($location.$$html5 && $sniffer.history && oldState !== $location.$$state);

        if (initializing || urlOrStateChanged) {
          initializing = false;

          $rootScope.$evalAsync(function() {
            var newUrl = $location.absUrl();
            var defaultPrevented = $rootScope.$broadcast('$locationChangeStart', newUrl, oldUrl,
                $location.$$state, oldState).defaultPrevented;

            // if the location was changed by a `$locationChangeStart` handler then stop
            // processing this location change
            if ($location.absUrl() !== newUrl) return;

            if (defaultPrevented) {
              $location.$$parse(oldUrl);
              $location.$$state = oldState;
            } else {
              if (urlOrStateChanged) {
                setBrowserUrlWithFallback(newUrl, currentReplace,
                                          oldState === $location.$$state ? null : $location.$$state);
              }
              afterLocationChange(oldUrl, oldState);
            }
          });
        }
      }

      $location.$$replace = false;

      // we don't need to return anything because $evalAsync will make the digest loop dirty when
      // there is a change
    });

    return $location;

    function afterLocationChange(oldUrl, oldState) {
      $rootScope.$broadcast('$locationChangeSuccess', $location.absUrl(), oldUrl,
        $location.$$state, oldState);
    }
}];
}

/**
 * @ngdoc service
 * @name $log
 * @requires $window
 *
 * @description
 * Simple service for logging. Default implementation safely writes the message
 * into the browser's console (if present).
 *
 * The main purpose of this service is to simplify debugging and troubleshooting.
 *
 * To reveal the location of the calls to `$log` in the JavaScript console,
 * you can "blackbox" the AngularJS source in your browser:
 *
 * [Mozilla description of blackboxing](https://developer.mozilla.org/en-US/docs/Tools/Debugger/How_to/Black_box_a_source).
 * [Chrome description of blackboxing](https://developer.chrome.com/devtools/docs/blackboxing).
 *
 * Note: Not all browsers support blackboxing.
 *
 * The default is to log `debug` messages. You can use
 * {@link ng.$logProvider ng.$logProvider#debugEnabled} to change this.
 *
 * @example
   <example module="logExample" name="log-service">
     <file name="script.js">
       angular.module('logExample', [])
         .controller('LogController', ['$scope', '$log', function($scope, $log) {
           $scope.$log = $log;
           $scope.message = 'Hello World!';
         }]);
     </file>
     <file name="index.html">
       <div ng-controller="LogController">
         <p>Reload this page with open console, enter text and hit the log button...</p>
         <label>Message:
         <input type="text" ng-model="message" /></label>
         <button ng-click="$log.log(message)">log</button>
         <button ng-click="$log.warn(message)">warn</button>
         <button ng-click="$log.info(message)">info</button>
         <button ng-click="$log.error(message)">error</button>
         <button ng-click="$log.debug(message)">debug</button>
       </div>
     </file>
   </example>
 */

/**
 * @ngdoc provider
 * @name $logProvider
 * @this
 *
 * @description
 * Use the `$logProvider` to configure how the application logs messages
 */
function $LogProvider() {
  var debug = true,
      self = this;

  /**
   * @ngdoc method
   * @name $logProvider#debugEnabled
   * @description
   * @param {boolean=} flag enable or disable debug level messages
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   */
  this.debugEnabled = function(flag) {
    if (isDefined(flag)) {
      debug = flag;
      return this;
    } else {
      return debug;
    }
  };

  this.$get = ['$window', function($window) {
    // Support: IE 9-11, Edge 12-14+
    // IE/Edge display errors in such a way that it requires the user to click in 4 places
    // to see the stack trace. There is no way to feature-detect it so there's a chance
    // of the user agent sniffing to go wrong but since it's only about logging, this shouldn't
    // break apps. Other browsers display errors in a sensible way and some of them map stack
    // traces along source maps if available so it makes sense to let browsers display it
    // as they want.
    var formatStackTrace = msie || /\bEdge\//.test($window.navigator && $window.navigator.userAgent);

    return {
      /**
       * @ngdoc method
       * @name $log#log
       *
       * @description
       * Write a log message
       */
      log: consoleLog('log'),

      /**
       * @ngdoc method
       * @name $log#info
       *
       * @description
       * Write an information message
       */
      info: consoleLog('info'),

      /**
       * @ngdoc method
       * @name $log#warn
       *
       * @description
       * Write a warning message
       */
      warn: consoleLog('warn'),

      /**
       * @ngdoc method
       * @name $log#error
       *
       * @description
       * Write an error message
       */
      error: consoleLog('error'),

      /**
       * @ngdoc method
       * @name $log#debug
       *
       * @description
       * Write a debug message
       */
      debug: (function() {
        var fn = consoleLog('debug');

        return function() {
          if (debug) {
            fn.apply(self, arguments);
          }
        };
      })()
    };

    function formatError(arg) {
      if (isError(arg)) {
        if (arg.stack && formatStackTrace) {
          arg = (arg.message && arg.stack.indexOf(arg.message) === -1)
              ? 'Error: ' + arg.message + '\n' + arg.stack
              : arg.stack;
        } else if (arg.sourceURL) {
          arg = arg.message + '\n' + arg.sourceURL + ':' + arg.line;
        }
      }
      return arg;
    }

    function consoleLog(type) {
      var console = $window.console || {},
          logFn = console[type] || console.log || noop;

      return function() {
        var args = [];
        forEach(arguments, function(arg) {
          args.push(formatError(arg));
        });
        // Support: IE 9 only
        // console methods don't inherit from Function.prototype in IE 9 so we can't
        // call `logFn.apply(console, args)` directly.
        return Function.prototype.apply.call(logFn, console, args);
      };
    }
  }];
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $parseMinErr = minErr('$parse');

var objectValueOf = {}.constructor.prototype.valueOf;

// Sandboxing Angular Expressions
// ------------------------------
// Angular expressions are no longer sandboxed. So it is now even easier to access arbitrary JS code by
// various means such as obtaining a reference to native JS functions like the Function constructor.
//
// As an example, consider the following Angular expression:
//
//   {}.toString.constructor('alert("evil JS code")')
//
// It is important to realize that if you create an expression from a string that contains user provided
// content then it is possible that your application contains a security vulnerability to an XSS style attack.
//
// See https://docs.angularjs.org/guide/security


function getStringValue(name) {
  // Property names must be strings. This means that non-string objects cannot be used
  // as keys in an object. Any non-string object, including a number, is typecasted
  // into a string via the toString method.
  // -- MDN, https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Operators/Property_accessors#Property_names
  //
  // So, to ensure that we are checking the same `name` that JavaScript would use, we cast it
  // to a string. It's not always possible. If `name` is an object and its `toString` method is
  // 'broken' (doesn't return a string, isn't a function, etc.), an error will be thrown:
  //
  // TypeError: Cannot convert object to primitive value
  //
  // For performance reasons, we don't catch this error here and allow it to propagate up the call
  // stack. Note that you'll get the same error in JavaScript if you try to access a property using
  // such a 'broken' object as a key.
  return name + '';
}


var OPERATORS = createMap();
forEach('+ - * / % === !== == != < > <= >= && || ! = |'.split(' '), function(operator) { OPERATORS[operator] = true; });
var ESCAPE = {'n':'\n', 'f':'\f', 'r':'\r', 't':'\t', 'v':'\v', '\'':'\'', '"':'"'};


/////////////////////////////////////////


/**
 * @constructor
 */
var Lexer = function Lexer(options) {
  this.options = options;
};

Lexer.prototype = {
  constructor: Lexer,

  lex: function(text) {
    this.text = text;
    this.index = 0;
    this.tokens = [];

    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      if (ch === '"' || ch === '\'') {
        this.readString(ch);
      } else if (this.isNumber(ch) || ch === '.' && this.isNumber(this.peek())) {
        this.readNumber();
      } else if (this.isIdentifierStart(this.peekMultichar())) {
        this.readIdent();
      } else if (this.is(ch, '(){}[].,;:?')) {
        this.tokens.push({index: this.index, text: ch});
        this.index++;
      } else if (this.isWhitespace(ch)) {
        this.index++;
      } else {
        var ch2 = ch + this.peek();
        var ch3 = ch2 + this.peek(2);
        var op1 = OPERATORS[ch];
        var op2 = OPERATORS[ch2];
        var op3 = OPERATORS[ch3];
        if (op1 || op2 || op3) {
          var token = op3 ? ch3 : (op2 ? ch2 : ch);
          this.tokens.push({index: this.index, text: token, operator: true});
          this.index += token.length;
        } else {
          this.throwError('Unexpected next character ', this.index, this.index + 1);
        }
      }
    }
    return this.tokens;
  },

  is: function(ch, chars) {
    return chars.indexOf(ch) !== -1;
  },

  peek: function(i) {
    var num = i || 1;
    return (this.index + num < this.text.length) ? this.text.charAt(this.index + num) : false;
  },

  isNumber: function(ch) {
    return ('0' <= ch && ch <= '9') && typeof ch === 'string';
  },

  isWhitespace: function(ch) {
    // IE treats non-breaking space as \u00A0
    return (ch === ' ' || ch === '\r' || ch === '\t' ||
            ch === '\n' || ch === '\v' || ch === '\u00A0');
  },

  isIdentifierStart: function(ch) {
    return this.options.isIdentifierStart ?
        this.options.isIdentifierStart(ch, this.codePointAt(ch)) :
        this.isValidIdentifierStart(ch);
  },

  isValidIdentifierStart: function(ch) {
    return ('a' <= ch && ch <= 'z' ||
            'A' <= ch && ch <= 'Z' ||
            '_' === ch || ch === '$');
  },

  isIdentifierContinue: function(ch) {
    return this.options.isIdentifierContinue ?
        this.options.isIdentifierContinue(ch, this.codePointAt(ch)) :
        this.isValidIdentifierContinue(ch);
  },

  isValidIdentifierContinue: function(ch, cp) {
    return this.isValidIdentifierStart(ch, cp) || this.isNumber(ch);
  },

  codePointAt: function(ch) {
    if (ch.length === 1) return ch.charCodeAt(0);
    // eslint-disable-next-line no-bitwise
    return (ch.charCodeAt(0) << 10) + ch.charCodeAt(1) - 0x35FDC00;
  },

  peekMultichar: function() {
    var ch = this.text.charAt(this.index);
    var peek = this.peek();
    if (!peek) {
      return ch;
    }
    var cp1 = ch.charCodeAt(0);
    var cp2 = peek.charCodeAt(0);
    if (cp1 >= 0xD800 && cp1 <= 0xDBFF && cp2 >= 0xDC00 && cp2 <= 0xDFFF) {
      return ch + peek;
    }
    return ch;
  },

  isExpOperator: function(ch) {
    return (ch === '-' || ch === '+' || this.isNumber(ch));
  },

  throwError: function(error, start, end) {
    end = end || this.index;
    var colStr = (isDefined(start)
            ? 's ' + start +  '-' + this.index + ' [' + this.text.substring(start, end) + ']'
            : ' ' + end);
    throw $parseMinErr('lexerr', 'Lexer Error: {0} at column{1} in expression [{2}].',
        error, colStr, this.text);
  },

  readNumber: function() {
    var number = '';
    var start = this.index;
    while (this.index < this.text.length) {
      var ch = lowercase(this.text.charAt(this.index));
      if (ch === '.' || this.isNumber(ch)) {
        number += ch;
      } else {
        var peekCh = this.peek();
        if (ch === 'e' && this.isExpOperator(peekCh)) {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            peekCh && this.isNumber(peekCh) &&
            number.charAt(number.length - 1) === 'e') {
          number += ch;
        } else if (this.isExpOperator(ch) &&
            (!peekCh || !this.isNumber(peekCh)) &&
            number.charAt(number.length - 1) === 'e') {
          this.throwError('Invalid exponent');
        } else {
          break;
        }
      }
      this.index++;
    }
    this.tokens.push({
      index: start,
      text: number,
      constant: true,
      value: Number(number)
    });
  },

  readIdent: function() {
    var start = this.index;
    this.index += this.peekMultichar().length;
    while (this.index < this.text.length) {
      var ch = this.peekMultichar();
      if (!this.isIdentifierContinue(ch)) {
        break;
      }
      this.index += ch.length;
    }
    this.tokens.push({
      index: start,
      text: this.text.slice(start, this.index),
      identifier: true
    });
  },

  readString: function(quote) {
    var start = this.index;
    this.index++;
    var string = '';
    var rawString = quote;
    var escape = false;
    while (this.index < this.text.length) {
      var ch = this.text.charAt(this.index);
      rawString += ch;
      if (escape) {
        if (ch === 'u') {
          var hex = this.text.substring(this.index + 1, this.index + 5);
          if (!hex.match(/[\da-f]{4}/i)) {
            this.throwError('Invalid unicode escape [\\u' + hex + ']');
          }
          this.index += 4;
          string += String.fromCharCode(parseInt(hex, 16));
        } else {
          var rep = ESCAPE[ch];
          string = string + (rep || ch);
        }
        escape = false;
      } else if (ch === '\\') {
        escape = true;
      } else if (ch === quote) {
        this.index++;
        this.tokens.push({
          index: start,
          text: rawString,
          constant: true,
          value: string
        });
        return;
      } else {
        string += ch;
      }
      this.index++;
    }
    this.throwError('Unterminated quote', start);
  }
};

var AST = function AST(lexer, options) {
  this.lexer = lexer;
  this.options = options;
};

AST.Program = 'Program';
AST.ExpressionStatement = 'ExpressionStatement';
AST.AssignmentExpression = 'AssignmentExpression';
AST.ConditionalExpression = 'ConditionalExpression';
AST.LogicalExpression = 'LogicalExpression';
AST.BinaryExpression = 'BinaryExpression';
AST.UnaryExpression = 'UnaryExpression';
AST.CallExpression = 'CallExpression';
AST.MemberExpression = 'MemberExpression';
AST.Identifier = 'Identifier';
AST.Literal = 'Literal';
AST.ArrayExpression = 'ArrayExpression';
AST.Property = 'Property';
AST.ObjectExpression = 'ObjectExpression';
AST.ThisExpression = 'ThisExpression';
AST.LocalsExpression = 'LocalsExpression';

// Internal use only
AST.NGValueParameter = 'NGValueParameter';

AST.prototype = {
  ast: function(text) {
    this.text = text;
    this.tokens = this.lexer.lex(text);

    var value = this.program();

    if (this.tokens.length !== 0) {
      this.throwError('is an unexpected token', this.tokens[0]);
    }

    return value;
  },

  program: function() {
    var body = [];
    while (true) {
      if (this.tokens.length > 0 && !this.peek('}', ')', ';', ']'))
        body.push(this.expressionStatement());
      if (!this.expect(';')) {
        return { type: AST.Program, body: body};
      }
    }
  },

  expressionStatement: function() {
    return { type: AST.ExpressionStatement, expression: this.filterChain() };
  },

  filterChain: function() {
    var left = this.expression();
    while (this.expect('|')) {
      left = this.filter(left);
    }
    return left;
  },

  expression: function() {
    return this.assignment();
  },

  assignment: function() {
    var result = this.ternary();
    if (this.expect('=')) {
      if (!isAssignable(result)) {
        throw $parseMinErr('lval', 'Trying to assign a value to a non l-value');
      }

      result = { type: AST.AssignmentExpression, left: result, right: this.assignment(), operator: '='};
    }
    return result;
  },

  ternary: function() {
    var test = this.logicalOR();
    var alternate;
    var consequent;
    if (this.expect('?')) {
      alternate = this.expression();
      if (this.consume(':')) {
        consequent = this.expression();
        return { type: AST.ConditionalExpression, test: test, alternate: alternate, consequent: consequent};
      }
    }
    return test;
  },

  logicalOR: function() {
    var left = this.logicalAND();
    while (this.expect('||')) {
      left = { type: AST.LogicalExpression, operator: '||', left: left, right: this.logicalAND() };
    }
    return left;
  },

  logicalAND: function() {
    var left = this.equality();
    while (this.expect('&&')) {
      left = { type: AST.LogicalExpression, operator: '&&', left: left, right: this.equality()};
    }
    return left;
  },

  equality: function() {
    var left = this.relational();
    var token;
    while ((token = this.expect('==','!=','===','!=='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.relational() };
    }
    return left;
  },

  relational: function() {
    var left = this.additive();
    var token;
    while ((token = this.expect('<', '>', '<=', '>='))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.additive() };
    }
    return left;
  },

  additive: function() {
    var left = this.multiplicative();
    var token;
    while ((token = this.expect('+','-'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.multiplicative() };
    }
    return left;
  },

  multiplicative: function() {
    var left = this.unary();
    var token;
    while ((token = this.expect('*','/','%'))) {
      left = { type: AST.BinaryExpression, operator: token.text, left: left, right: this.unary() };
    }
    return left;
  },

  unary: function() {
    var token;
    if ((token = this.expect('+', '-', '!'))) {
      return { type: AST.UnaryExpression, operator: token.text, prefix: true, argument: this.unary() };
    } else {
      return this.primary();
    }
  },

  primary: function() {
    var primary;
    if (this.expect('(')) {
      primary = this.filterChain();
      this.consume(')');
    } else if (this.expect('[')) {
      primary = this.arrayDeclaration();
    } else if (this.expect('{')) {
      primary = this.object();
    } else if (this.selfReferential.hasOwnProperty(this.peek().text)) {
      primary = copy(this.selfReferential[this.consume().text]);
    } else if (this.options.literals.hasOwnProperty(this.peek().text)) {
      primary = { type: AST.Literal, value: this.options.literals[this.consume().text]};
    } else if (this.peek().identifier) {
      primary = this.identifier();
    } else if (this.peek().constant) {
      primary = this.constant();
    } else {
      this.throwError('not a primary expression', this.peek());
    }

    var next;
    while ((next = this.expect('(', '[', '.'))) {
      if (next.text === '(') {
        primary = {type: AST.CallExpression, callee: primary, arguments: this.parseArguments() };
        this.consume(')');
      } else if (next.text === '[') {
        primary = { type: AST.MemberExpression, object: primary, property: this.expression(), computed: true };
        this.consume(']');
      } else if (next.text === '.') {
        primary = { type: AST.MemberExpression, object: primary, property: this.identifier(), computed: false };
      } else {
        this.throwError('IMPOSSIBLE');
      }
    }
    return primary;
  },

  filter: function(baseExpression) {
    var args = [baseExpression];
    var result = {type: AST.CallExpression, callee: this.identifier(), arguments: args, filter: true};

    while (this.expect(':')) {
      args.push(this.expression());
    }

    return result;
  },

  parseArguments: function() {
    var args = [];
    if (this.peekToken().text !== ')') {
      do {
        args.push(this.filterChain());
      } while (this.expect(','));
    }
    return args;
  },

  identifier: function() {
    var token = this.consume();
    if (!token.identifier) {
      this.throwError('is not a valid identifier', token);
    }
    return { type: AST.Identifier, name: token.text };
  },

  constant: function() {
    // TODO check that it is a constant
    return { type: AST.Literal, value: this.consume().value };
  },

  arrayDeclaration: function() {
    var elements = [];
    if (this.peekToken().text !== ']') {
      do {
        if (this.peek(']')) {
          // Support trailing commas per ES5.1.
          break;
        }
        elements.push(this.expression());
      } while (this.expect(','));
    }
    this.consume(']');

    return { type: AST.ArrayExpression, elements: elements };
  },

  object: function() {
    var properties = [], property;
    if (this.peekToken().text !== '}') {
      do {
        if (this.peek('}')) {
          // Support trailing commas per ES5.1.
          break;
        }
        property = {type: AST.Property, kind: 'init'};
        if (this.peek().constant) {
          property.key = this.constant();
          property.computed = false;
          this.consume(':');
          property.value = this.expression();
        } else if (this.peek().identifier) {
          property.key = this.identifier();
          property.computed = false;
          if (this.peek(':')) {
            this.consume(':');
            property.value = this.expression();
          } else {
            property.value = property.key;
          }
        } else if (this.peek('[')) {
          this.consume('[');
          property.key = this.expression();
          this.consume(']');
          property.computed = true;
          this.consume(':');
          property.value = this.expression();
        } else {
          this.throwError('invalid key', this.peek());
        }
        properties.push(property);
      } while (this.expect(','));
    }
    this.consume('}');

    return {type: AST.ObjectExpression, properties: properties };
  },

  throwError: function(msg, token) {
    throw $parseMinErr('syntax',
        'Syntax Error: Token \'{0}\' {1} at column {2} of the expression [{3}] starting at [{4}].',
          token.text, msg, (token.index + 1), this.text, this.text.substring(token.index));
  },

  consume: function(e1) {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }

    var token = this.expect(e1);
    if (!token) {
      this.throwError('is unexpected, expecting [' + e1 + ']', this.peek());
    }
    return token;
  },

  peekToken: function() {
    if (this.tokens.length === 0) {
      throw $parseMinErr('ueoe', 'Unexpected end of expression: {0}', this.text);
    }
    return this.tokens[0];
  },

  peek: function(e1, e2, e3, e4) {
    return this.peekAhead(0, e1, e2, e3, e4);
  },

  peekAhead: function(i, e1, e2, e3, e4) {
    if (this.tokens.length > i) {
      var token = this.tokens[i];
      var t = token.text;
      if (t === e1 || t === e2 || t === e3 || t === e4 ||
          (!e1 && !e2 && !e3 && !e4)) {
        return token;
      }
    }
    return false;
  },

  expect: function(e1, e2, e3, e4) {
    var token = this.peek(e1, e2, e3, e4);
    if (token) {
      this.tokens.shift();
      return token;
    }
    return false;
  },

  selfReferential: {
    'this': {type: AST.ThisExpression },
    '$locals': {type: AST.LocalsExpression }
  }
};

function ifDefined(v, d) {
  return typeof v !== 'undefined' ? v : d;
}

function plusFn(l, r) {
  if (typeof l === 'undefined') return r;
  if (typeof r === 'undefined') return l;
  return l + r;
}

function isStateless($filter, filterName) {
  var fn = $filter(filterName);
  return !fn.$stateful;
}

var PURITY_ABSOLUTE = 1;
var PURITY_RELATIVE = 2;

// Detect nodes which could depend on non-shallow state of objects
function isPure(node, parentIsPure) {
  switch (node.type) {
    // Computed members might invoke a stateful toString()
    case AST.MemberExpression:
      if (node.computed) {
        return false;
      }
      break;

    // Unary always convert to primative
    case AST.UnaryExpression:
      return PURITY_ABSOLUTE;

    // The binary + operator can invoke a stateful toString().
    case AST.BinaryExpression:
      return node.operator !== '+' ? PURITY_ABSOLUTE : false;

    // Functions / filters probably read state from within objects
    case AST.CallExpression:
      return false;
  }

  return (undefined === parentIsPure) ? PURITY_RELATIVE : parentIsPure;
}

function findConstantAndWatchExpressions(ast, $filter, parentIsPure) {
  var allConstants;
  var argsToWatch;
  var isStatelessFilter;

  var astIsPure = ast.isPure = isPure(ast, parentIsPure);

  switch (ast.type) {
  case AST.Program:
    allConstants = true;
    forEach(ast.body, function(expr) {
      findConstantAndWatchExpressions(expr.expression, $filter, astIsPure);
      allConstants = allConstants && expr.expression.constant;
    });
    ast.constant = allConstants;
    break;
  case AST.Literal:
    ast.constant = true;
    ast.toWatch = [];
    break;
  case AST.UnaryExpression:
    findConstantAndWatchExpressions(ast.argument, $filter, astIsPure);
    ast.constant = ast.argument.constant;
    ast.toWatch = ast.argument.toWatch;
    break;
  case AST.BinaryExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.left.toWatch.concat(ast.right.toWatch);
    break;
  case AST.LogicalExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.ConditionalExpression:
    findConstantAndWatchExpressions(ast.test, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.alternate, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.consequent, $filter, astIsPure);
    ast.constant = ast.test.constant && ast.alternate.constant && ast.consequent.constant;
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.Identifier:
    ast.constant = false;
    ast.toWatch = [ast];
    break;
  case AST.MemberExpression:
    findConstantAndWatchExpressions(ast.object, $filter, astIsPure);
    if (ast.computed) {
      findConstantAndWatchExpressions(ast.property, $filter, astIsPure);
    }
    ast.constant = ast.object.constant && (!ast.computed || ast.property.constant);
    ast.toWatch = ast.constant ? [] : [ast];
    break;
  case AST.CallExpression:
    isStatelessFilter = ast.filter ? isStateless($filter, ast.callee.name) : false;
    allConstants = isStatelessFilter;
    argsToWatch = [];
    forEach(ast.arguments, function(expr) {
      findConstantAndWatchExpressions(expr, $filter, astIsPure);
      allConstants = allConstants && expr.constant;
      argsToWatch.push.apply(argsToWatch, expr.toWatch);
    });
    ast.constant = allConstants;
    ast.toWatch = isStatelessFilter ? argsToWatch : [ast];
    break;
  case AST.AssignmentExpression:
    findConstantAndWatchExpressions(ast.left, $filter, astIsPure);
    findConstantAndWatchExpressions(ast.right, $filter, astIsPure);
    ast.constant = ast.left.constant && ast.right.constant;
    ast.toWatch = [ast];
    break;
  case AST.ArrayExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.elements, function(expr) {
      findConstantAndWatchExpressions(expr, $filter, astIsPure);
      allConstants = allConstants && expr.constant;
      argsToWatch.push.apply(argsToWatch, expr.toWatch);
    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ObjectExpression:
    allConstants = true;
    argsToWatch = [];
    forEach(ast.properties, function(property) {
      findConstantAndWatchExpressions(property.value, $filter, astIsPure);
      allConstants = allConstants && property.value.constant;
      argsToWatch.push.apply(argsToWatch, property.value.toWatch);
      if (property.computed) {
        //`{[key]: value}` implicitly does `key.toString()` which may be non-pure
        findConstantAndWatchExpressions(property.key, $filter, /*parentIsPure=*/false);
        allConstants = allConstants && property.key.constant;
        argsToWatch.push.apply(argsToWatch, property.key.toWatch);
      }
    });
    ast.constant = allConstants;
    ast.toWatch = argsToWatch;
    break;
  case AST.ThisExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  case AST.LocalsExpression:
    ast.constant = false;
    ast.toWatch = [];
    break;
  }
}

function getInputs(body) {
  if (body.length !== 1) return;
  var lastExpression = body[0].expression;
  var candidate = lastExpression.toWatch;
  if (candidate.length !== 1) return candidate;
  return candidate[0] !== lastExpression ? candidate : undefined;
}

function isAssignable(ast) {
  return ast.type === AST.Identifier || ast.type === AST.MemberExpression;
}

function assignableAST(ast) {
  if (ast.body.length === 1 && isAssignable(ast.body[0].expression)) {
    return {type: AST.AssignmentExpression, left: ast.body[0].expression, right: {type: AST.NGValueParameter}, operator: '='};
  }
}

function isLiteral(ast) {
  return ast.body.length === 0 ||
      ast.body.length === 1 && (
      ast.body[0].expression.type === AST.Literal ||
      ast.body[0].expression.type === AST.ArrayExpression ||
      ast.body[0].expression.type === AST.ObjectExpression);
}

function isConstant(ast) {
  return ast.constant;
}

function ASTCompiler($filter) {
  this.$filter = $filter;
}

ASTCompiler.prototype = {
  compile: function(ast) {
    var self = this;
    this.state = {
      nextId: 0,
      filters: {},
      fn: {vars: [], body: [], own: {}},
      assign: {vars: [], body: [], own: {}},
      inputs: []
    };
    findConstantAndWatchExpressions(ast, self.$filter);
    var extra = '';
    var assignable;
    this.stage = 'assign';
    if ((assignable = assignableAST(ast))) {
      this.state.computing = 'assign';
      var result = this.nextId();
      this.recurse(assignable, result);
      this.return_(result);
      extra = 'fn.assign=' + this.generateFunction('assign', 's,v,l');
    }
    var toWatch = getInputs(ast.body);
    self.stage = 'inputs';
    forEach(toWatch, function(watch, key) {
      var fnKey = 'fn' + key;
      self.state[fnKey] = {vars: [], body: [], own: {}};
      self.state.computing = fnKey;
      var intoId = self.nextId();
      self.recurse(watch, intoId);
      self.return_(intoId);
      self.state.inputs.push({name: fnKey, isPure: watch.isPure});
      watch.watchId = key;
    });
    this.state.computing = 'fn';
    this.stage = 'main';
    this.recurse(ast);
    var fnString =
      // The build and minification steps remove the string "use strict" from the code, but this is done using a regex.
      // This is a workaround for this until we do a better job at only removing the prefix only when we should.
      '"' + this.USE + ' ' + this.STRICT + '";\n' +
      this.filterPrefix() +
      'var fn=' + this.generateFunction('fn', 's,l,a,i') +
      extra +
      this.watchFns() +
      'return fn;';

    // eslint-disable-next-line no-new-func
    var fn = (new Function('$filter',
        'getStringValue',
        'ifDefined',
        'plus',
        fnString))(
          this.$filter,
          getStringValue,
          ifDefined,
          plusFn);
    this.state = this.stage = undefined;
    return fn;
  },

  USE: 'use',

  STRICT: 'strict',

  watchFns: function() {
    var result = [];
    var inputs = this.state.inputs;
    var self = this;
    forEach(inputs, function(input) {
      result.push('var ' + input.name + '=' + self.generateFunction(input.name, 's'));
      if (input.isPure) {
        result.push(input.name, '.isPure=' + JSON.stringify(input.isPure) + ';');
      }
    });
    if (inputs.length) {
      result.push('fn.inputs=[' + inputs.map(function(i) { return i.name; }).join(',') + '];');
    }
    return result.join('');
  },

  generateFunction: function(name, params) {
    return 'function(' + params + '){' +
        this.varsPrefix(name) +
        this.body(name) +
        '};';
  },

  filterPrefix: function() {
    var parts = [];
    var self = this;
    forEach(this.state.filters, function(id, filter) {
      parts.push(id + '=$filter(' + self.escape(filter) + ')');
    });
    if (parts.length) return 'var ' + parts.join(',') + ';';
    return '';
  },

  varsPrefix: function(section) {
    return this.state[section].vars.length ? 'var ' + this.state[section].vars.join(',') + ';' : '';
  },

  body: function(section) {
    return this.state[section].body.join('');
  },

  recurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var left, right, self = this, args, expression, computed;
    recursionFn = recursionFn || noop;
    if (!skipWatchIdCheck && isDefined(ast.watchId)) {
      intoId = intoId || this.nextId();
      this.if_('i',
        this.lazyAssign(intoId, this.computedMember('i', ast.watchId)),
        this.lazyRecurse(ast, intoId, nameId, recursionFn, create, true)
      );
      return;
    }
    switch (ast.type) {
    case AST.Program:
      forEach(ast.body, function(expression, pos) {
        self.recurse(expression.expression, undefined, undefined, function(expr) { right = expr; });
        if (pos !== ast.body.length - 1) {
          self.current().body.push(right, ';');
        } else {
          self.return_(right);
        }
      });
      break;
    case AST.Literal:
      expression = this.escape(ast.value);
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.UnaryExpression:
      this.recurse(ast.argument, undefined, undefined, function(expr) { right = expr; });
      expression = ast.operator + '(' + this.ifDefined(right, 0) + ')';
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.BinaryExpression:
      this.recurse(ast.left, undefined, undefined, function(expr) { left = expr; });
      this.recurse(ast.right, undefined, undefined, function(expr) { right = expr; });
      if (ast.operator === '+') {
        expression = this.plus(left, right);
      } else if (ast.operator === '-') {
        expression = this.ifDefined(left, 0) + ast.operator + this.ifDefined(right, 0);
      } else {
        expression = '(' + left + ')' + ast.operator + '(' + right + ')';
      }
      this.assign(intoId, expression);
      recursionFn(expression);
      break;
    case AST.LogicalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.left, intoId);
      self.if_(ast.operator === '&&' ? intoId : self.not(intoId), self.lazyRecurse(ast.right, intoId));
      recursionFn(intoId);
      break;
    case AST.ConditionalExpression:
      intoId = intoId || this.nextId();
      self.recurse(ast.test, intoId);
      self.if_(intoId, self.lazyRecurse(ast.alternate, intoId), self.lazyRecurse(ast.consequent, intoId));
      recursionFn(intoId);
      break;
    case AST.Identifier:
      intoId = intoId || this.nextId();
      if (nameId) {
        nameId.context = self.stage === 'inputs' ? 's' : this.assign(this.nextId(), this.getHasOwnProperty('l', ast.name) + '?l:s');
        nameId.computed = false;
        nameId.name = ast.name;
      }
      self.if_(self.stage === 'inputs' || self.not(self.getHasOwnProperty('l', ast.name)),
        function() {
          self.if_(self.stage === 'inputs' || 's', function() {
            if (create && create !== 1) {
              self.if_(
                self.isNull(self.nonComputedMember('s', ast.name)),
                self.lazyAssign(self.nonComputedMember('s', ast.name), '{}'));
            }
            self.assign(intoId, self.nonComputedMember('s', ast.name));
          });
        }, intoId && self.lazyAssign(intoId, self.nonComputedMember('l', ast.name))
        );
      recursionFn(intoId);
      break;
    case AST.MemberExpression:
      left = nameId && (nameId.context = this.nextId()) || this.nextId();
      intoId = intoId || this.nextId();
      self.recurse(ast.object, left, undefined, function() {
        self.if_(self.notNull(left), function() {
          if (ast.computed) {
            right = self.nextId();
            self.recurse(ast.property, right);
            self.getStringValue(right);
            if (create && create !== 1) {
              self.if_(self.not(self.computedMember(left, right)), self.lazyAssign(self.computedMember(left, right), '{}'));
            }
            expression = self.computedMember(left, right);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = true;
              nameId.name = right;
            }
          } else {
            if (create && create !== 1) {
              self.if_(self.isNull(self.nonComputedMember(left, ast.property.name)), self.lazyAssign(self.nonComputedMember(left, ast.property.name), '{}'));
            }
            expression = self.nonComputedMember(left, ast.property.name);
            self.assign(intoId, expression);
            if (nameId) {
              nameId.computed = false;
              nameId.name = ast.property.name;
            }
          }
        }, function() {
          self.assign(intoId, 'undefined');
        });
        recursionFn(intoId);
      }, !!create);
      break;
    case AST.CallExpression:
      intoId = intoId || this.nextId();
      if (ast.filter) {
        right = self.filter(ast.callee.name);
        args = [];
        forEach(ast.arguments, function(expr) {
          var argument = self.nextId();
          self.recurse(expr, argument);
          args.push(argument);
        });
        expression = right + '(' + args.join(',') + ')';
        self.assign(intoId, expression);
        recursionFn(intoId);
      } else {
        right = self.nextId();
        left = {};
        args = [];
        self.recurse(ast.callee, right, left, function() {
          self.if_(self.notNull(right), function() {
            forEach(ast.arguments, function(expr) {
              self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
                args.push(argument);
              });
            });
            if (left.name) {
              expression = self.member(left.context, left.name, left.computed) + '(' + args.join(',') + ')';
            } else {
              expression = right + '(' + args.join(',') + ')';
            }
            self.assign(intoId, expression);
          }, function() {
            self.assign(intoId, 'undefined');
          });
          recursionFn(intoId);
        });
      }
      break;
    case AST.AssignmentExpression:
      right = this.nextId();
      left = {};
      this.recurse(ast.left, undefined, left, function() {
        self.if_(self.notNull(left.context), function() {
          self.recurse(ast.right, right);
          expression = self.member(left.context, left.name, left.computed) + ast.operator + right;
          self.assign(intoId, expression);
          recursionFn(intoId || expression);
        });
      }, 1);
      break;
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        self.recurse(expr, ast.constant ? undefined : self.nextId(), undefined, function(argument) {
          args.push(argument);
        });
      });
      expression = '[' + args.join(',') + ']';
      this.assign(intoId, expression);
      recursionFn(intoId || expression);
      break;
    case AST.ObjectExpression:
      args = [];
      computed = false;
      forEach(ast.properties, function(property) {
        if (property.computed) {
          computed = true;
        }
      });
      if (computed) {
        intoId = intoId || this.nextId();
        this.assign(intoId, '{}');
        forEach(ast.properties, function(property) {
          if (property.computed) {
            left = self.nextId();
            self.recurse(property.key, left);
          } else {
            left = property.key.type === AST.Identifier ?
                       property.key.name :
                       ('' + property.key.value);
          }
          right = self.nextId();
          self.recurse(property.value, right);
          self.assign(self.member(intoId, left, property.computed), right);
        });
      } else {
        forEach(ast.properties, function(property) {
          self.recurse(property.value, ast.constant ? undefined : self.nextId(), undefined, function(expr) {
            args.push(self.escape(
                property.key.type === AST.Identifier ? property.key.name :
                  ('' + property.key.value)) +
                ':' + expr);
          });
        });
        expression = '{' + args.join(',') + '}';
        this.assign(intoId, expression);
      }
      recursionFn(intoId || expression);
      break;
    case AST.ThisExpression:
      this.assign(intoId, 's');
      recursionFn(intoId || 's');
      break;
    case AST.LocalsExpression:
      this.assign(intoId, 'l');
      recursionFn(intoId || 'l');
      break;
    case AST.NGValueParameter:
      this.assign(intoId, 'v');
      recursionFn(intoId || 'v');
      break;
    }
  },

  getHasOwnProperty: function(element, property) {
    var key = element + '.' + property;
    var own = this.current().own;
    if (!own.hasOwnProperty(key)) {
      own[key] = this.nextId(false, element + '&&(' + this.escape(property) + ' in ' + element + ')');
    }
    return own[key];
  },

  assign: function(id, value) {
    if (!id) return;
    this.current().body.push(id, '=', value, ';');
    return id;
  },

  filter: function(filterName) {
    if (!this.state.filters.hasOwnProperty(filterName)) {
      this.state.filters[filterName] = this.nextId(true);
    }
    return this.state.filters[filterName];
  },

  ifDefined: function(id, defaultValue) {
    return 'ifDefined(' + id + ',' + this.escape(defaultValue) + ')';
  },

  plus: function(left, right) {
    return 'plus(' + left + ',' + right + ')';
  },

  return_: function(id) {
    this.current().body.push('return ', id, ';');
  },

  if_: function(test, alternate, consequent) {
    if (test === true) {
      alternate();
    } else {
      var body = this.current().body;
      body.push('if(', test, '){');
      alternate();
      body.push('}');
      if (consequent) {
        body.push('else{');
        consequent();
        body.push('}');
      }
    }
  },

  not: function(expression) {
    return '!(' + expression + ')';
  },

  isNull: function(expression) {
    return expression + '==null';
  },

  notNull: function(expression) {
    return expression + '!=null';
  },

  nonComputedMember: function(left, right) {
    var SAFE_IDENTIFIER = /^[$_a-zA-Z][$_a-zA-Z0-9]*$/;
    var UNSAFE_CHARACTERS = /[^$_a-zA-Z0-9]/g;
    if (SAFE_IDENTIFIER.test(right)) {
      return left + '.' + right;
    } else {
      return left  + '["' + right.replace(UNSAFE_CHARACTERS, this.stringEscapeFn) + '"]';
    }
  },

  computedMember: function(left, right) {
    return left + '[' + right + ']';
  },

  member: function(left, right, computed) {
    if (computed) return this.computedMember(left, right);
    return this.nonComputedMember(left, right);
  },

  getStringValue: function(item) {
    this.assign(item, 'getStringValue(' + item + ')');
  },

  lazyRecurse: function(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck) {
    var self = this;
    return function() {
      self.recurse(ast, intoId, nameId, recursionFn, create, skipWatchIdCheck);
    };
  },

  lazyAssign: function(id, value) {
    var self = this;
    return function() {
      self.assign(id, value);
    };
  },

  stringEscapeRegex: /[^ a-zA-Z0-9]/g,

  stringEscapeFn: function(c) {
    return '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4);
  },

  escape: function(value) {
    if (isString(value)) return '\'' + value.replace(this.stringEscapeRegex, this.stringEscapeFn) + '\'';
    if (isNumber(value)) return value.toString();
    if (value === true) return 'true';
    if (value === false) return 'false';
    if (value === null) return 'null';
    if (typeof value === 'undefined') return 'undefined';

    throw $parseMinErr('esc', 'IMPOSSIBLE');
  },

  nextId: function(skip, init) {
    var id = 'v' + (this.state.nextId++);
    if (!skip) {
      this.current().vars.push(id + (init ? '=' + init : ''));
    }
    return id;
  },

  current: function() {
    return this.state[this.state.computing];
  }
};


function ASTInterpreter($filter) {
  this.$filter = $filter;
}

ASTInterpreter.prototype = {
  compile: function(ast) {
    var self = this;
    findConstantAndWatchExpressions(ast, self.$filter);
    var assignable;
    var assign;
    if ((assignable = assignableAST(ast))) {
      assign = this.recurse(assignable);
    }
    var toWatch = getInputs(ast.body);
    var inputs;
    if (toWatch) {
      inputs = [];
      forEach(toWatch, function(watch, key) {
        var input = self.recurse(watch);
        input.isPure = watch.isPure;
        watch.input = input;
        inputs.push(input);
        watch.watchId = key;
      });
    }
    var expressions = [];
    forEach(ast.body, function(expression) {
      expressions.push(self.recurse(expression.expression));
    });
    var fn = ast.body.length === 0 ? noop :
             ast.body.length === 1 ? expressions[0] :
             function(scope, locals) {
               var lastValue;
               forEach(expressions, function(exp) {
                 lastValue = exp(scope, locals);
               });
               return lastValue;
             };
    if (assign) {
      fn.assign = function(scope, value, locals) {
        return assign(scope, locals, value);
      };
    }
    if (inputs) {
      fn.inputs = inputs;
    }
    return fn;
  },

  recurse: function(ast, context, create) {
    var left, right, self = this, args;
    if (ast.input) {
      return this.inputs(ast.input, ast.watchId);
    }
    switch (ast.type) {
    case AST.Literal:
      return this.value(ast.value, context);
    case AST.UnaryExpression:
      right = this.recurse(ast.argument);
      return this['unary' + ast.operator](right, context);
    case AST.BinaryExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.LogicalExpression:
      left = this.recurse(ast.left);
      right = this.recurse(ast.right);
      return this['binary' + ast.operator](left, right, context);
    case AST.ConditionalExpression:
      return this['ternary?:'](
        this.recurse(ast.test),
        this.recurse(ast.alternate),
        this.recurse(ast.consequent),
        context
      );
    case AST.Identifier:
      return self.identifier(ast.name, context, create);
    case AST.MemberExpression:
      left = this.recurse(ast.object, false, !!create);
      if (!ast.computed) {
        right = ast.property.name;
      }
      if (ast.computed) right = this.recurse(ast.property);
      return ast.computed ?
        this.computedMember(left, right, context, create) :
        this.nonComputedMember(left, right, context, create);
    case AST.CallExpression:
      args = [];
      forEach(ast.arguments, function(expr) {
        args.push(self.recurse(expr));
      });
      if (ast.filter) right = this.$filter(ast.callee.name);
      if (!ast.filter) right = this.recurse(ast.callee, true);
      return ast.filter ?
        function(scope, locals, assign, inputs) {
          var values = [];
          for (var i = 0; i < args.length; ++i) {
            values.push(args[i](scope, locals, assign, inputs));
          }
          var value = right.apply(undefined, values, inputs);
          return context ? {context: undefined, name: undefined, value: value} : value;
        } :
        function(scope, locals, assign, inputs) {
          var rhs = right(scope, locals, assign, inputs);
          var value;
          if (rhs.value != null) {
            var values = [];
            for (var i = 0; i < args.length; ++i) {
              values.push(args[i](scope, locals, assign, inputs));
            }
            value = rhs.value.apply(rhs.context, values);
          }
          return context ? {value: value} : value;
        };
    case AST.AssignmentExpression:
      left = this.recurse(ast.left, true, 1);
      right = this.recurse(ast.right);
      return function(scope, locals, assign, inputs) {
        var lhs = left(scope, locals, assign, inputs);
        var rhs = right(scope, locals, assign, inputs);
        lhs.context[lhs.name] = rhs;
        return context ? {value: rhs} : rhs;
      };
    case AST.ArrayExpression:
      args = [];
      forEach(ast.elements, function(expr) {
        args.push(self.recurse(expr));
      });
      return function(scope, locals, assign, inputs) {
        var value = [];
        for (var i = 0; i < args.length; ++i) {
          value.push(args[i](scope, locals, assign, inputs));
        }
        return context ? {value: value} : value;
      };
    case AST.ObjectExpression:
      args = [];
      forEach(ast.properties, function(property) {
        if (property.computed) {
          args.push({key: self.recurse(property.key),
                     computed: true,
                     value: self.recurse(property.value)
          });
        } else {
          args.push({key: property.key.type === AST.Identifier ?
                          property.key.name :
                          ('' + property.key.value),
                     computed: false,
                     value: self.recurse(property.value)
          });
        }
      });
      return function(scope, locals, assign, inputs) {
        var value = {};
        for (var i = 0; i < args.length; ++i) {
          if (args[i].computed) {
            value[args[i].key(scope, locals, assign, inputs)] = args[i].value(scope, locals, assign, inputs);
          } else {
            value[args[i].key] = args[i].value(scope, locals, assign, inputs);
          }
        }
        return context ? {value: value} : value;
      };
    case AST.ThisExpression:
      return function(scope) {
        return context ? {value: scope} : scope;
      };
    case AST.LocalsExpression:
      return function(scope, locals) {
        return context ? {value: locals} : locals;
      };
    case AST.NGValueParameter:
      return function(scope, locals, assign) {
        return context ? {value: assign} : assign;
      };
    }
  },

  'unary+': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = +arg;
      } else {
        arg = 0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary-': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = argument(scope, locals, assign, inputs);
      if (isDefined(arg)) {
        arg = -arg;
      } else {
        arg = -0;
      }
      return context ? {value: arg} : arg;
    };
  },
  'unary!': function(argument, context) {
    return function(scope, locals, assign, inputs) {
      var arg = !argument(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary+': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = plusFn(lhs, rhs);
      return context ? {value: arg} : arg;
    };
  },
  'binary-': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs = right(scope, locals, assign, inputs);
      var arg = (isDefined(lhs) ? lhs : 0) - (isDefined(rhs) ? rhs : 0);
      return context ? {value: arg} : arg;
    };
  },
  'binary*': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) * right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary/': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) / right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary%': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) % right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary===': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) === right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) !== right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary==': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) == right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary!=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      // eslint-disable-next-line eqeqeq
      var arg = left(scope, locals, assign, inputs) != right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) < right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) > right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary<=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) <= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary>=': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) >= right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary&&': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) && right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'binary||': function(left, right, context) {
    return function(scope, locals, assign, inputs) {
      var arg = left(scope, locals, assign, inputs) || right(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  'ternary?:': function(test, alternate, consequent, context) {
    return function(scope, locals, assign, inputs) {
      var arg = test(scope, locals, assign, inputs) ? alternate(scope, locals, assign, inputs) : consequent(scope, locals, assign, inputs);
      return context ? {value: arg} : arg;
    };
  },
  value: function(value, context) {
    return function() { return context ? {context: undefined, name: undefined, value: value} : value; };
  },
  identifier: function(name, context, create) {
    return function(scope, locals, assign, inputs) {
      var base = locals && (name in locals) ? locals : scope;
      if (create && create !== 1 && base && base[name] == null) {
        base[name] = {};
      }
      var value = base ? base[name] : undefined;
      if (context) {
        return {context: base, name: name, value: value};
      } else {
        return value;
      }
    };
  },
  computedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      var rhs;
      var value;
      if (lhs != null) {
        rhs = right(scope, locals, assign, inputs);
        rhs = getStringValue(rhs);
        if (create && create !== 1) {
          if (lhs && !(lhs[rhs])) {
            lhs[rhs] = {};
          }
        }
        value = lhs[rhs];
      }
      if (context) {
        return {context: lhs, name: rhs, value: value};
      } else {
        return value;
      }
    };
  },
  nonComputedMember: function(left, right, context, create) {
    return function(scope, locals, assign, inputs) {
      var lhs = left(scope, locals, assign, inputs);
      if (create && create !== 1) {
        if (lhs && lhs[right] == null) {
          lhs[right] = {};
        }
      }
      var value = lhs != null ? lhs[right] : undefined;
      if (context) {
        return {context: lhs, name: right, value: value};
      } else {
        return value;
      }
    };
  },
  inputs: function(input, watchId) {
    return function(scope, value, locals, inputs) {
      if (inputs) return inputs[watchId];
      return input(scope, value, locals);
    };
  }
};

/**
 * @constructor
 */
function Parser(lexer, $filter, options) {
  this.ast = new AST(lexer, options);
  this.astCompiler = options.csp ? new ASTInterpreter($filter) :
                                   new ASTCompiler($filter);
}

Parser.prototype = {
  constructor: Parser,

  parse: function(text) {
    var ast = this.ast.ast(text);
    var fn = this.astCompiler.compile(ast);
    fn.literal = isLiteral(ast);
    fn.constant = isConstant(ast);
    return fn;
  }
};

function getValueOf(value) {
  return isFunction(value.valueOf) ? value.valueOf() : objectValueOf.call(value);
}

///////////////////////////////////

/**
 * @ngdoc service
 * @name $parse
 * @kind function
 *
 * @description
 *
 * Converts Angular {@link guide/expression expression} into a function.
 *
 * ```js
 *   var getter = $parse('user.name');
 *   var setter = getter.assign;
 *   var context = {user:{name:'angular'}};
 *   var locals = {user:{name:'local'}};
 *
 *   expect(getter(context)).toEqual('angular');
 *   setter(context, 'newValue');
 *   expect(context.user.name).toEqual('newValue');
 *   expect(getter(context, locals)).toEqual('local');
 * ```
 *
 *
 * @param {string} expression String expression to compile.
 * @returns {function(context, locals)} a function which represents the compiled expression:
 *
 *    * `context` – `{object}` – an object against which any expressions embedded in the strings
 *      are evaluated against (typically a scope object).
 *    * `locals` – `{object=}` – local variables context object, useful for overriding values in
 *      `context`.
 *
 *    The returned function also has the following properties:
 *      * `literal` – `{boolean}` – whether the expression's top-level node is a JavaScript
 *        literal.
 *      * `constant` – `{boolean}` – whether the expression is made entirely of JavaScript
 *        constant literals.
 *      * `assign` – `{?function(context, value)}` – if the expression is assignable, this will be
 *        set to a function to change its value on the given context.
 *
 */


/**
 * @ngdoc provider
 * @name $parseProvider
 * @this
 *
 * @description
 * `$parseProvider` can be used for configuring the default behavior of the {@link ng.$parse $parse}
 *  service.
 */
function $ParseProvider() {
  var cache = createMap();
  var literals = {
    'true': true,
    'false': false,
    'null': null,
    'undefined': undefined
  };
  var identStart, identContinue;

  /**
   * @ngdoc method
   * @name $parseProvider#addLiteral
   * @description
   *
   * Configure $parse service to add literal values that will be present as literal at expressions.
   *
   * @param {string} literalName Token for the literal value. The literal name value must be a valid literal name.
   * @param {*} literalValue Value for this literal. All literal values must be primitives or `undefined`.
   *
   **/
  this.addLiteral = function(literalName, literalValue) {
    literals[literalName] = literalValue;
  };

 /**
  * @ngdoc method
  * @name $parseProvider#setIdentifierFns
  *
  * @description
  *
  * Allows defining the set of characters that are allowed in Angular expressions. The function
  * `identifierStart` will get called to know if a given character is a valid character to be the
  * first character for an identifier. The function `identifierContinue` will get called to know if
  * a given character is a valid character to be a follow-up identifier character. The functions
  * `identifierStart` and `identifierContinue` will receive as arguments the single character to be
  * identifier and the character code point. These arguments will be `string` and `numeric`. Keep in
  * mind that the `string` parameter can be two characters long depending on the character
  * representation. It is expected for the function to return `true` or `false`, whether that
  * character is allowed or not.
  *
  * Since this function will be called extensively, keep the implementation of these functions fast,
  * as the performance of these functions have a direct impact on the expressions parsing speed.
  *
  * @param {function=} identifierStart The function that will decide whether the given character is
  *   a valid identifier start character.
  * @param {function=} identifierContinue The function that will decide whether the given character is
  *   a valid identifier continue character.
  */
  this.setIdentifierFns = function(identifierStart, identifierContinue) {
    identStart = identifierStart;
    identContinue = identifierContinue;
    return this;
  };

  this.$get = ['$filter', function($filter) {
    var noUnsafeEval = csp().noUnsafeEval;
    var $parseOptions = {
          csp: noUnsafeEval,
          literals: copy(literals),
          isIdentifierStart: isFunction(identStart) && identStart,
          isIdentifierContinue: isFunction(identContinue) && identContinue
        };
    return $parse;

    function $parse(exp, interceptorFn) {
      var parsedExpression, oneTime, cacheKey;

      switch (typeof exp) {
        case 'string':
          exp = exp.trim();
          cacheKey = exp;

          parsedExpression = cache[cacheKey];

          if (!parsedExpression) {
            if (exp.charAt(0) === ':' && exp.charAt(1) === ':') {
              oneTime = true;
              exp = exp.substring(2);
            }
            var lexer = new Lexer($parseOptions);
            var parser = new Parser(lexer, $filter, $parseOptions);
            parsedExpression = parser.parse(exp);
            if (parsedExpression.constant) {
              parsedExpression.$$watchDelegate = constantWatchDelegate;
            } else if (oneTime) {
              parsedExpression.$$watchDelegate = parsedExpression.literal ?
                  oneTimeLiteralWatchDelegate : oneTimeWatchDelegate;
            } else if (parsedExpression.inputs) {
              parsedExpression.$$watchDelegate = inputsWatchDelegate;
            }
            cache[cacheKey] = parsedExpression;
          }
          return addInterceptor(parsedExpression, interceptorFn);

        case 'function':
          return addInterceptor(exp, interceptorFn);

        default:
          return addInterceptor(noop, interceptorFn);
      }
    }

    function expressionInputDirtyCheck(newValue, oldValueOfValue, compareObjectIdentity) {

      if (newValue == null || oldValueOfValue == null) { // null/undefined
        return newValue === oldValueOfValue;
      }

      if (typeof newValue === 'object') {

        // attempt to convert the value to a primitive type
        // TODO(docs): add a note to docs that by implementing valueOf even objects and arrays can
        //             be cheaply dirty-checked
        newValue = getValueOf(newValue);

        if (typeof newValue === 'object' && !compareObjectIdentity) {
          // objects/arrays are not supported - deep-watching them would be too expensive
          return false;
        }

        // fall-through to the primitive equality check
      }

      //Primitive or NaN
      // eslint-disable-next-line no-self-compare
      return newValue === oldValueOfValue || (newValue !== newValue && oldValueOfValue !== oldValueOfValue);
    }

    function inputsWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var inputExpressions = parsedExpression.inputs;
      var lastResult;

      if (inputExpressions.length === 1) {
        var oldInputValueOf = expressionInputDirtyCheck; // init to something unique so that equals check fails
        inputExpressions = inputExpressions[0];
        return scope.$watch(function expressionInputWatch(scope) {
          var newInputValue = inputExpressions(scope);
          if (!expressionInputDirtyCheck(newInputValue, oldInputValueOf, inputExpressions.isPure)) {
            lastResult = parsedExpression(scope, undefined, undefined, [newInputValue]);
            oldInputValueOf = newInputValue && getValueOf(newInputValue);
          }
          return lastResult;
        }, listener, objectEquality, prettyPrintExpression);
      }

      var oldInputValueOfValues = [];
      var oldInputValues = [];
      for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
        oldInputValueOfValues[i] = expressionInputDirtyCheck; // init to something unique so that equals check fails
        oldInputValues[i] = null;
      }

      return scope.$watch(function expressionInputsWatch(scope) {
        var changed = false;

        for (var i = 0, ii = inputExpressions.length; i < ii; i++) {
          var newInputValue = inputExpressions[i](scope);
          if (changed || (changed = !expressionInputDirtyCheck(newInputValue, oldInputValueOfValues[i], inputExpressions[i].isPure))) {
            oldInputValues[i] = newInputValue;
            oldInputValueOfValues[i] = newInputValue && getValueOf(newInputValue);
          }
        }

        if (changed) {
          lastResult = parsedExpression(scope, undefined, undefined, oldInputValues);
        }

        return lastResult;
      }, listener, objectEquality, prettyPrintExpression);
    }

    function oneTimeWatchDelegate(scope, listener, objectEquality, parsedExpression, prettyPrintExpression) {
      var unwatch, lastValue;
      if (parsedExpression.inputs) {
        unwatch = inputsWatchDelegate(scope, oneTimeListener, objectEquality, parsedExpression, prettyPrintExpression);
      } else {
        unwatch = scope.$watch(oneTimeWatch, oneTimeListener, objectEquality);
      }
      return unwatch;

      function oneTimeWatch(scope) {
        return parsedExpression(scope);
      }
      function oneTimeListener(value, old, scope) {
        lastValue = value;
        if (isFunction(listener)) {
          listener(value, old, scope);
        }
        if (isDefined(value)) {
          scope.$$postDigest(function() {
            if (isDefined(lastValue)) {
              unwatch();
            }
          });
        }
      }
    }

    function oneTimeLiteralWatchDelegate(scope, listener, objectEquality, parsedExpression) {
      var unwatch, lastValue;
      unwatch = scope.$watch(function oneTimeWatch(scope) {
        return parsedExpression(scope);
      }, function oneTimeListener(value, old, scope) {
        lastValue = value;
        if (isFunction(listener)) {
          listener(value, old, scope);
        }
        if (isAllDefined(value)) {
          scope.$$postDigest(function() {
            if (isAllDefined(lastValue)) unwatch();
          });
        }
      }, objectEquality);

      return unwatch;

      function isAllDefined(value) {
        var allDefined = true;
        forEach(value, function(val) {
          if (!isDefined(val)) allDefined = false;
        });
        return allDefined;
      }
    }

    function constantWatchDelegate(scope, listener, objectEquality, parsedExpression) {
      var unwatch = scope.$watch(function constantWatch(scope) {
        unwatch();
        return parsedExpression(scope);
      }, listener, objectEquality);
      return unwatch;
    }

    function addInterceptor(parsedExpression, interceptorFn) {
      if (!interceptorFn) return parsedExpression;
      var watchDelegate = parsedExpression.$$watchDelegate;
      var useInputs = false;

      var regularWatch =
          watchDelegate !== oneTimeLiteralWatchDelegate &&
          watchDelegate !== oneTimeWatchDelegate;

      var fn = regularWatch ? function regularInterceptedExpression(scope, locals, assign, inputs) {
        var value = useInputs && inputs ? inputs[0] : parsedExpression(scope, locals, assign, inputs);
        return interceptorFn(value, scope, locals);
      } : function oneTimeInterceptedExpression(scope, locals, assign, inputs) {
        var value = parsedExpression(scope, locals, assign, inputs);
        var result = interceptorFn(value, scope, locals);
        // we only return the interceptor's result if the
        // initial value is defined (for bind-once)
        return isDefined(value) ? result : value;
      };

      // Propagate $$watchDelegates other then inputsWatchDelegate
      useInputs = !parsedExpression.inputs;
      if (watchDelegate && watchDelegate !== inputsWatchDelegate) {
        fn.$$watchDelegate = watchDelegate;
        fn.inputs = parsedExpression.inputs;
      } else if (!interceptorFn.$stateful) {
        // Treat interceptor like filters - assume non-stateful by default and use the inputsWatchDelegate
        fn.$$watchDelegate = inputsWatchDelegate;
        fn.inputs = parsedExpression.inputs ? parsedExpression.inputs : [parsedExpression];
      }

      if (fn.inputs) {
        fn.inputs = fn.inputs.map(function(e) {
              // Remove the isPure flag of inputs when it is not absolute because they are now wrapped in a
              // potentially non-pure interceptor function.
              if (e.isPure === PURITY_RELATIVE) {
                return function depurifier(s) { return e(s); };
              }
              return e;
            });
      }

      return fn;
    }
  }];
}

/**
 * @ngdoc service
 * @name $q
 * @requires $rootScope
 *
 * @description
 * A service that helps you run functions asynchronously, and use their return values (or exceptions)
 * when they are done processing.
 *
 * This is a [Promises/A+](https://promisesaplus.com/)-compliant implementation of promises/deferred
 * objects inspired by [Kris Kowal's Q](https://github.com/kriskowal/q).
 *
 * $q can be used in two fashions --- one which is more similar to Kris Kowal's Q or jQuery's Deferred
 * implementations, and the other which resembles ES6 (ES2015) promises to some degree.
 *
 * # $q constructor
 *
 * The streamlined ES6 style promise is essentially just using $q as a constructor which takes a `resolver`
 * function as the first argument. This is similar to the native Promise implementation from ES6,
 * see [MDN](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise).
 *
 * While the constructor-style use is supported, not all of the supporting methods from ES6 promises are
 * available yet.
 *
 * It can be used like so:
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     // perform some asynchronous operation, resolve or reject the promise when appropriate.
 *     return $q(function(resolve, reject) {
 *       setTimeout(function() {
 *         if (okToGreet(name)) {
 *           resolve('Hello, ' + name + '!');
 *         } else {
 *           reject('Greeting ' + name + ' is not allowed.');
 *         }
 *       }, 1000);
 *     });
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   });
 * ```
 *
 * Note: progress/notify callbacks are not currently supported via the ES6-style interface.
 *
 * Note: unlike ES6 behavior, an exception thrown in the constructor function will NOT implicitly reject the promise.
 *
 * However, the more traditional CommonJS-style usage is still available, and documented below.
 *
 * [The CommonJS Promise proposal](http://wiki.commonjs.org/wiki/Promises) describes a promise as an
 * interface for interacting with an object that represents the result of an action that is
 * performed asynchronously, and may or may not be finished at any given point in time.
 *
 * From the perspective of dealing with error handling, deferred and promise APIs are to
 * asynchronous programming what `try`, `catch` and `throw` keywords are to synchronous programming.
 *
 * ```js
 *   // for the purpose of this example let's assume that variables `$q` and `okToGreet`
 *   // are available in the current lexical scope (they could have been injected or passed in).
 *
 *   function asyncGreet(name) {
 *     var deferred = $q.defer();
 *
 *     setTimeout(function() {
 *       deferred.notify('About to greet ' + name + '.');
 *
 *       if (okToGreet(name)) {
 *         deferred.resolve('Hello, ' + name + '!');
 *       } else {
 *         deferred.reject('Greeting ' + name + ' is not allowed.');
 *       }
 *     }, 1000);
 *
 *     return deferred.promise;
 *   }
 *
 *   var promise = asyncGreet('Robin Hood');
 *   promise.then(function(greeting) {
 *     alert('Success: ' + greeting);
 *   }, function(reason) {
 *     alert('Failed: ' + reason);
 *   }, function(update) {
 *     alert('Got notification: ' + update);
 *   });
 * ```
 *
 * At first it might not be obvious why this extra complexity is worth the trouble. The payoff
 * comes in the way of guarantees that promise and deferred APIs make, see
 * https://github.com/kriskowal/uncommonjs/blob/master/promises/specification.md.
 *
 * Additionally the promise api allows for composition that is very hard to do with the
 * traditional callback ([CPS](http://en.wikipedia.org/wiki/Continuation-passing_style)) approach.
 * For more on this please see the [Q documentation](https://github.com/kriskowal/q) especially the
 * section on serial or parallel joining of promises.
 *
 * # The Deferred API
 *
 * A new instance of deferred is constructed by calling `$q.defer()`.
 *
 * The purpose of the deferred object is to expose the associated Promise instance as well as APIs
 * that can be used for signaling the successful or unsuccessful completion, as well as the status
 * of the task.
 *
 * **Methods**
 *
 * - `resolve(value)` – resolves the derived promise with the `value`. If the value is a rejection
 *   constructed via `$q.reject`, the promise will be rejected instead.
 * - `reject(reason)` – rejects the derived promise with the `reason`. This is equivalent to
 *   resolving it with a rejection constructed via `$q.reject`.
 * - `notify(value)` - provides updates on the status of the promise's execution. This may be called
 *   multiple times before the promise is either resolved or rejected.
 *
 * **Properties**
 *
 * - promise – `{Promise}` – promise object associated with this deferred.
 *
 *
 * # The Promise API
 *
 * A new promise instance is created when a deferred instance is created and can be retrieved by
 * calling `deferred.promise`.
 *
 * The purpose of the promise object is to allow for interested parties to get access to the result
 * of the deferred task when it completes.
 *
 * **Methods**
 *
 * - `then(successCallback, [errorCallback], [notifyCallback])` – regardless of when the promise was or
 *   will be resolved or rejected, `then` calls one of the success or error callbacks asynchronously
 *   as soon as the result is available. The callbacks are called with a single argument: the result
 *   or rejection reason. Additionally, the notify callback may be called zero or more times to
 *   provide a progress indication, before the promise is resolved or rejected.
 *
 *   This method *returns a new promise* which is resolved or rejected via the return value of the
 *   `successCallback`, `errorCallback` (unless that value is a promise, in which case it is resolved
 *   with the value which is resolved in that promise using
 *   [promise chaining](http://www.html5rocks.com/en/tutorials/es6/promises/#toc-promises-queues)).
 *   It also notifies via the return value of the `notifyCallback` method. The promise cannot be
 *   resolved or rejected from the notifyCallback method. The errorCallback and notifyCallback
 *   arguments are optional.
 *
 * - `catch(errorCallback)` – shorthand for `promise.then(null, errorCallback)`
 *
 * - `finally(callback, notifyCallback)` – allows you to observe either the fulfillment or rejection of a promise,
 *   but to do so without modifying the final value. This is useful to release resources or do some
 *   clean-up that needs to be done whether the promise was rejected or resolved. See the [full
 *   specification](https://github.com/kriskowal/q/wiki/API-Reference#promisefinallycallback) for
 *   more information.
 *
 * # Chaining promises
 *
 * Because calling the `then` method of a promise returns a new derived promise, it is easily
 * possible to create a chain of promises:
 *
 * ```js
 *   promiseB = promiseA.then(function(result) {
 *     return result + 1;
 *   });
 *
 *   // promiseB will be resolved immediately after promiseA is resolved and its value
 *   // will be the result of promiseA incremented by 1
 * ```
 *
 * It is possible to create chains of any length and since a promise can be resolved with another
 * promise (which will defer its resolution further), it is possible to pause/defer resolution of
 * the promises at any point in the chain. This makes it possible to implement powerful APIs like
 * $http's response interceptors.
 *
 *
 * # Differences between Kris Kowal's Q and $q
 *
 *  There are two main differences:
 *
 * - $q is integrated with the {@link ng.$rootScope.Scope} Scope model observation
 *   mechanism in angular, which means faster propagation of resolution or rejection into your
 *   models and avoiding unnecessary browser repaints, which would result in flickering UI.
 * - Q has many more features than $q, but that comes at a cost of bytes. $q is tiny, but contains
 *   all the important functionality needed for common async tasks.
 *
 * # Testing
 *
 *  ```js
 *    it('should simulate promise', inject(function($q, $rootScope) {
 *      var deferred = $q.defer();
 *      var promise = deferred.promise;
 *      var resolvedValue;
 *
 *      promise.then(function(value) { resolvedValue = value; });
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Simulate resolving of promise
 *      deferred.resolve(123);
 *      // Note that the 'then' function does not get called synchronously.
 *      // This is because we want the promise API to always be async, whether or not
 *      // it got called synchronously or asynchronously.
 *      expect(resolvedValue).toBeUndefined();
 *
 *      // Propagate promise resolution to 'then' functions using $apply().
 *      $rootScope.$apply();
 *      expect(resolvedValue).toEqual(123);
 *    }));
 *  ```
 *
 * @param {function(function, function)} resolver Function which is responsible for resolving or
 *   rejecting the newly created promise. The first parameter is a function which resolves the
 *   promise, the second parameter is a function which rejects the promise.
 *
 * @returns {Promise} The newly created promise.
 */
/**
 * @ngdoc provider
 * @name $qProvider
 * @this
 *
 * @description
 */
function $QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$rootScope', '$exceptionHandler', function($rootScope, $exceptionHandler) {
    return qFactory(function(callback) {
      $rootScope.$evalAsync(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  /**
   * @ngdoc method
   * @name $qProvider#errorOnUnhandledRejections
   * @kind function
   *
   * @description
   * Retrieves or overrides whether to generate an error when a rejected promise is not handled.
   * This feature is enabled by default.
   *
   * @param {boolean=} value Whether to generate an error when a rejected promise is not handled.
   * @returns {boolean|ng.$qProvider} Current value when called without a new value or self for
   *    chaining otherwise.
   */
  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/** @this */
function $$QProvider() {
  var errorOnUnhandledRejections = true;
  this.$get = ['$browser', '$exceptionHandler', function($browser, $exceptionHandler) {
    return qFactory(function(callback) {
      $browser.defer(callback);
    }, $exceptionHandler, errorOnUnhandledRejections);
  }];

  this.errorOnUnhandledRejections = function(value) {
    if (isDefined(value)) {
      errorOnUnhandledRejections = value;
      return this;
    } else {
      return errorOnUnhandledRejections;
    }
  };
}

/**
 * Constructs a promise manager.
 *
 * @param {function(function)} nextTick Function for executing functions in the next turn.
 * @param {function(...*)} exceptionHandler Function into which unexpected exceptions are passed for
 *     debugging purposes.
 * @param {boolean=} errorOnUnhandledRejections Whether an error should be generated on unhandled
 *     promises rejections.
 * @returns {object} Promise manager.
 */
function qFactory(nextTick, exceptionHandler, errorOnUnhandledRejections) {
  var $qMinErr = minErr('$q', TypeError);
  var queueSize = 0;
  var checkQueue = [];

  /**
   * @ngdoc method
   * @name ng.$q#defer
   * @kind function
   *
   * @description
   * Creates a `Deferred` object which represents a task which will finish in the future.
   *
   * @returns {Deferred} Returns a new instance of deferred.
   */
  function defer() {
    return new Deferred();
  }

  function Deferred() {
    var promise = this.promise = new Promise();
    //Non prototype methods necessary to support unbound execution :/
    this.resolve = function(val) { resolvePromise(promise, val); };
    this.reject = function(reason) { rejectPromise(promise, reason); };
    this.notify = function(progress) { notifyPromise(promise, progress); };
  }


  function Promise() {
    this.$$state = { status: 0 };
  }

  extend(Promise.prototype, {
    then: function(onFulfilled, onRejected, progressBack) {
      if (isUndefined(onFulfilled) && isUndefined(onRejected) && isUndefined(progressBack)) {
        return this;
      }
      var result = new Promise();

      this.$$state.pending = this.$$state.pending || [];
      this.$$state.pending.push([result, onFulfilled, onRejected, progressBack]);
      if (this.$$state.status > 0) scheduleProcessQueue(this.$$state);

      return result;
    },

    'catch': function(callback) {
      return this.then(null, callback);
    },

    'finally': function(callback, progressBack) {
      return this.then(function(value) {
        return handleCallback(value, resolve, callback);
      }, function(error) {
        return handleCallback(error, reject, callback);
      }, progressBack);
    }
  });

  function processQueue(state) {
    var fn, promise, pending;

    pending = state.pending;
    state.processScheduled = false;
    state.pending = undefined;
    try {
      for (var i = 0, ii = pending.length; i < ii; ++i) {
        markQStateExceptionHandled(state);
        promise = pending[i][0];
        fn = pending[i][state.status];
        try {
          if (isFunction(fn)) {
            resolvePromise(promise, fn(state.value));
          } else if (state.status === 1) {
            resolvePromise(promise, state.value);
          } else {
            rejectPromise(promise, state.value);
          }
        } catch (e) {
          rejectPromise(promise, e);
        }
      }
    } finally {
      --queueSize;
      if (errorOnUnhandledRejections && queueSize === 0) {
        nextTick(processChecks);
      }
    }
  }

  function processChecks() {
    // eslint-disable-next-line no-unmodified-loop-condition
    while (!queueSize && checkQueue.length) {
      var toCheck = checkQueue.shift();
      if (!isStateExceptionHandled(toCheck)) {
        markQStateExceptionHandled(toCheck);
        var errorMessage = 'Possibly unhandled rejection: ' + toDebugString(toCheck.value);
        if (isError(toCheck.value)) {
          exceptionHandler(toCheck.value, errorMessage);
        } else {
          exceptionHandler(errorMessage);
        }
      }
    }
  }

  function scheduleProcessQueue(state) {
    if (errorOnUnhandledRejections && !state.pending && state.status === 2 && !isStateExceptionHandled(state)) {
      if (queueSize === 0 && checkQueue.length === 0) {
        nextTick(processChecks);
      }
      checkQueue.push(state);
    }
    if (state.processScheduled || !state.pending) return;
    state.processScheduled = true;
    ++queueSize;
    nextTick(function() { processQueue(state); });
  }

  function resolvePromise(promise, val) {
    if (promise.$$state.status) return;
    if (val === promise) {
      $$reject(promise, $qMinErr(
        'qcycle',
        'Expected promise to be resolved with value other than itself \'{0}\'',
        val));
    } else {
      $$resolve(promise, val);
    }

  }

  function $$resolve(promise, val) {
    var then;
    var done = false;
    try {
      if (isObject(val) || isFunction(val)) then = val.then;
      if (isFunction(then)) {
        promise.$$state.status = -1;
        then.call(val, doResolve, doReject, doNotify);
      } else {
        promise.$$state.value = val;
        promise.$$state.status = 1;
        scheduleProcessQueue(promise.$$state);
      }
    } catch (e) {
      doReject(e);
    }

    function doResolve(val) {
      if (done) return;
      done = true;
      $$resolve(promise, val);
    }
    function doReject(val) {
      if (done) return;
      done = true;
      $$reject(promise, val);
    }
    function doNotify(progress) {
      notifyPromise(promise, progress);
    }
  }

  function rejectPromise(promise, reason) {
    if (promise.$$state.status) return;
    $$reject(promise, reason);
  }

  function $$reject(promise, reason) {
    promise.$$state.value = reason;
    promise.$$state.status = 2;
    scheduleProcessQueue(promise.$$state);
  }

  function notifyPromise(promise, progress) {
    var callbacks = promise.$$state.pending;

    if ((promise.$$state.status <= 0) && callbacks && callbacks.length) {
      nextTick(function() {
        var callback, result;
        for (var i = 0, ii = callbacks.length; i < ii; i++) {
          result = callbacks[i][0];
          callback = callbacks[i][3];
          try {
            notifyPromise(result, isFunction(callback) ? callback(progress) : progress);
          } catch (e) {
            exceptionHandler(e);
          }
        }
      });
    }
  }

  /**
   * @ngdoc method
   * @name $q#reject
   * @kind function
   *
   * @description
   * Creates a promise that is resolved as rejected with the specified `reason`. This api should be
   * used to forward rejection in a chain of promises. If you are dealing with the last promise in
   * a promise chain, you don't need to worry about it.
   *
   * When comparing deferreds/promises to the familiar behavior of try/catch/throw, think of
   * `reject` as the `throw` keyword in JavaScript. This also means that if you "catch" an error via
   * a promise error callback and you want to forward the error to the promise derived from the
   * current promise, you have to "rethrow" the error by returning a rejection constructed via
   * `reject`.
   *
   * ```js
   *   promiseB = promiseA.then(function(result) {
   *     // success: do something and resolve promiseB
   *     //          with the old or a new result
   *     return result;
   *   }, function(reason) {
   *     // error: handle the error if possible and
   *     //        resolve promiseB with newPromiseOrValue,
   *     //        otherwise forward the rejection to promiseB
   *     if (canHandle(reason)) {
   *      // handle the error and recover
   *      return newPromiseOrValue;
   *     }
   *     return $q.reject(reason);
   *   });
   * ```
   *
   * @param {*} reason Constant, message, exception or an object representing the rejection reason.
   * @returns {Promise} Returns a promise that was already resolved as rejected with the `reason`.
   */
  function reject(reason) {
    var result = new Promise();
    rejectPromise(result, reason);
    return result;
  }

  function handleCallback(value, resolver, callback) {
    var callbackOutput = null;
    try {
      if (isFunction(callback)) callbackOutput = callback();
    } catch (e) {
      return reject(e);
    }
    if (isPromiseLike(callbackOutput)) {
      return callbackOutput.then(function() {
        return resolver(value);
      }, reject);
    } else {
      return resolver(value);
    }
  }

  /**
   * @ngdoc method
   * @name $q#when
   * @kind function
   *
   * @description
   * Wraps an object that might be a value or a (3rd party) then-able promise into a $q promise.
   * This is useful when you are dealing with an object that might or might not be a promise, or if
   * the promise comes from a source that can't be trusted.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */


  function when(value, callback, errback, progressBack) {
    var result = new Promise();
    resolvePromise(result, value);
    return result.then(callback, errback, progressBack);
  }

  /**
   * @ngdoc method
   * @name $q#resolve
   * @kind function
   *
   * @description
   * Alias of {@link ng.$q#when when} to maintain naming consistency with ES6.
   *
   * @param {*} value Value or a promise
   * @param {Function=} successCallback
   * @param {Function=} errorCallback
   * @param {Function=} progressCallback
   * @returns {Promise} Returns a promise of the passed value or promise
   */
  var resolve = when;

  /**
   * @ngdoc method
   * @name $q#all
   * @kind function
   *
   * @description
   * Combines multiple promises into a single promise that is resolved when all of the input
   * promises are resolved.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} Returns a single promise that will be resolved with an array/hash of values,
   *   each value corresponding to the promise at the same index/key in the `promises` array/hash.
   *   If any of the promises is resolved with a rejection, this resulting promise will be rejected
   *   with the same rejection value.
   */

  function all(promises) {
    var result = new Promise(),
        counter = 0,
        results = isArray(promises) ? [] : {};

    forEach(promises, function(promise, key) {
      counter++;
      when(promise).then(function(value) {
        results[key] = value;
        if (!(--counter)) resolvePromise(result, results);
      }, function(reason) {
        rejectPromise(result, reason);
      });
    });

    if (counter === 0) {
      resolvePromise(result, results);
    }

    return result;
  }

  /**
   * @ngdoc method
   * @name $q#race
   * @kind function
   *
   * @description
   * Returns a promise that resolves or rejects as soon as one of those promises
   * resolves or rejects, with the value or reason from that promise.
   *
   * @param {Array.<Promise>|Object.<Promise>} promises An array or hash of promises.
   * @returns {Promise} a promise that resolves or rejects as soon as one of the `promises`
   * resolves or rejects, with the value or reason from that promise.
   */

  function race(promises) {
    var deferred = defer();

    forEach(promises, function(promise) {
      when(promise).then(deferred.resolve, deferred.reject);
    });

    return deferred.promise;
  }

  function $Q(resolver) {
    if (!isFunction(resolver)) {
      throw $qMinErr('norslvr', 'Expected resolverFn, got \'{0}\'', resolver);
    }

    var promise = new Promise();

    function resolveFn(value) {
      resolvePromise(promise, value);
    }

    function rejectFn(reason) {
      rejectPromise(promise, reason);
    }

    resolver(resolveFn, rejectFn);

    return promise;
  }

  // Let's make the instanceof operator work for promises, so that
  // `new $q(fn) instanceof $q` would evaluate to true.
  $Q.prototype = Promise.prototype;

  $Q.defer = defer;
  $Q.reject = reject;
  $Q.when = when;
  $Q.resolve = resolve;
  $Q.all = all;
  $Q.race = race;

  return $Q;
}

function isStateExceptionHandled(state) {
  return !!state.pur;
}
function markQStateExceptionHandled(state) {
  state.pur = true;
}
function markQExceptionHandled(q) {
  markQStateExceptionHandled(q.$$state);
}

/** @this */
function $$RAFProvider() { //rAF
  this.$get = ['$window', '$timeout', function($window, $timeout) {
    var requestAnimationFrame = $window.requestAnimationFrame ||
                                $window.webkitRequestAnimationFrame;

    var cancelAnimationFrame = $window.cancelAnimationFrame ||
                               $window.webkitCancelAnimationFrame ||
                               $window.webkitCancelRequestAnimationFrame;

    var rafSupported = !!requestAnimationFrame;
    var raf = rafSupported
      ? function(fn) {
          var id = requestAnimationFrame(fn);
          return function() {
            cancelAnimationFrame(id);
          };
        }
      : function(fn) {
          var timer = $timeout(fn, 16.66, false); // 1000 / 60 = 16.666
          return function() {
            $timeout.cancel(timer);
          };
        };

    raf.supported = rafSupported;

    return raf;
  }];
}

/**
 * DESIGN NOTES
 *
 * The design decisions behind the scope are heavily favored for speed and memory consumption.
 *
 * The typical use of scope is to watch the expressions, which most of the time return the same
 * value as last time so we optimize the operation.
 *
 * Closures construction is expensive in terms of speed as well as memory:
 *   - No closures, instead use prototypical inheritance for API
 *   - Internal state needs to be stored on scope directly, which means that private state is
 *     exposed as $$____ properties
 *
 * Loop operations are optimized by using while(count--) { ... }
 *   - This means that in order to keep the same order of execution as addition we have to add
 *     items to the array at the beginning (unshift) instead of at the end (push)
 *
 * Child scopes are created and removed often
 *   - Using an array would be slow since inserts in the middle are expensive; so we use linked lists
 *
 * There are fewer watches than observers. This is why you don't want the observer to be implemented
 * in the same way as watch. Watch requires return of the initialization function which is expensive
 * to construct.
 */


/**
 * @ngdoc provider
 * @name $rootScopeProvider
 * @description
 *
 * Provider for the $rootScope service.
 */

/**
 * @ngdoc method
 * @name $rootScopeProvider#digestTtl
 * @description
 *
 * Sets the number of `$digest` iterations the scope should attempt to execute before giving up and
 * assuming that the model is unstable.
 *
 * The current default is 10 iterations.
 *
 * In complex applications it's possible that the dependencies between `$watch`s will result in
 * several digest iterations. However if an application needs more than the default 10 digest
 * iterations for its model to stabilize then you should investigate what is causing the model to
 * continuously change during the digest.
 *
 * Increasing the TTL could have performance implications, so you should not change it without
 * proper justification.
 *
 * @param {number} limit The number of digest iterations.
 */


/**
 * @ngdoc service
 * @name $rootScope
 * @this
 *
 * @description
 *
 * Every application has a single root {@link ng.$rootScope.Scope scope}.
 * All other scopes are descendant scopes of the root scope. Scopes provide separation
 * between the model and the view, via a mechanism for watching the model for changes.
 * They also provide event emission/broadcast and subscription facility. See the
 * {@link guide/scope developer guide on scopes}.
 */
function $RootScopeProvider() {
  var TTL = 10;
  var $rootScopeMinErr = minErr('$rootScope');
  var lastDirtyWatch = null;
  var applyAsyncId = null;

  this.digestTtl = function(value) {
    if (arguments.length) {
      TTL = value;
    }
    return TTL;
  };

  function createChildScopeClass(parent) {
    function ChildScope() {
      this.$$watchers = this.$$nextSibling =
          this.$$childHead = this.$$childTail = null;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$id = nextUid();
      this.$$ChildScope = null;
    }
    ChildScope.prototype = parent;
    return ChildScope;
  }

  this.$get = ['$exceptionHandler', '$parse', '$browser',
      function($exceptionHandler, $parse, $browser) {

    function destroyChildScope($event) {
        $event.currentScope.$$destroyed = true;
    }

    function cleanUpScope($scope) {

      // Support: IE 9 only
      if (msie === 9) {
        // There is a memory leak in IE9 if all child scopes are not disconnected
        // completely when a scope is destroyed. So this code will recurse up through
        // all this scopes children
        //
        // See issue https://github.com/angular/angular.js/issues/10706
        if ($scope.$$childHead) {
          cleanUpScope($scope.$$childHead);
        }
        if ($scope.$$nextSibling) {
          cleanUpScope($scope.$$nextSibling);
        }
      }

      // The code below works around IE9 and V8's memory leaks
      //
      // See:
      // - https://code.google.com/p/v8/issues/detail?id=2073#c26
      // - https://github.com/angular/angular.js/issues/6794#issuecomment-38648909
      // - https://github.com/angular/angular.js/issues/1313#issuecomment-10378451

      $scope.$parent = $scope.$$nextSibling = $scope.$$prevSibling = $scope.$$childHead =
          $scope.$$childTail = $scope.$root = $scope.$$watchers = null;
    }

    /**
     * @ngdoc type
     * @name $rootScope.Scope
     *
     * @description
     * A root scope can be retrieved using the {@link ng.$rootScope $rootScope} key from the
     * {@link auto.$injector $injector}. Child scopes are created using the
     * {@link ng.$rootScope.Scope#$new $new()} method. (Most scopes are created automatically when
     * compiled HTML template is executed.) See also the {@link guide/scope Scopes guide} for
     * an in-depth introduction and usage examples.
     *
     *
     * # Inheritance
     * A scope can inherit from a parent scope, as in this example:
     * ```js
         var parent = $rootScope;
         var child = parent.$new();

         parent.salutation = "Hello";
         expect(child.salutation).toEqual('Hello');

         child.salutation = "Welcome";
         expect(child.salutation).toEqual('Welcome');
         expect(parent.salutation).toEqual('Hello');
     * ```
     *
     * When interacting with `Scope` in tests, additional helper methods are available on the
     * instances of `Scope` type. See {@link ngMock.$rootScope.Scope ngMock Scope} for additional
     * details.
     *
     *
     * @param {Object.<string, function()>=} providers Map of service factory which need to be
     *                                       provided for the current scope. Defaults to {@link ng}.
     * @param {Object.<string, *>=} instanceCache Provides pre-instantiated services which should
     *                              append/override services provided by `providers`. This is handy
     *                              when unit-testing and having the need to override a default
     *                              service.
     * @returns {Object} Newly created scope.
     *
     */
    function Scope() {
      this.$id = nextUid();
      this.$$phase = this.$parent = this.$$watchers =
                     this.$$nextSibling = this.$$prevSibling =
                     this.$$childHead = this.$$childTail = null;
      this.$root = this;
      this.$$destroyed = false;
      this.$$listeners = {};
      this.$$listenerCount = {};
      this.$$watchersCount = 0;
      this.$$isolateBindings = null;
    }

    /**
     * @ngdoc property
     * @name $rootScope.Scope#$id
     *
     * @description
     * Unique scope ID (monotonically increasing) useful for debugging.
     */

     /**
      * @ngdoc property
      * @name $rootScope.Scope#$parent
      *
      * @description
      * Reference to the parent scope.
      */

      /**
       * @ngdoc property
       * @name $rootScope.Scope#$root
       *
       * @description
       * Reference to the root scope.
       */

    Scope.prototype = {
      constructor: Scope,
      /**
       * @ngdoc method
       * @name $rootScope.Scope#$new
       * @kind function
       *
       * @description
       * Creates a new child {@link ng.$rootScope.Scope scope}.
       *
       * The parent scope will propagate the {@link ng.$rootScope.Scope#$digest $digest()} event.
       * The scope can be removed from the scope hierarchy using {@link ng.$rootScope.Scope#$destroy $destroy()}.
       *
       * {@link ng.$rootScope.Scope#$destroy $destroy()} must be called on a scope when it is
       * desired for the scope and its child scopes to be permanently detached from the parent and
       * thus stop participating in model change detection and listener notification by invoking.
       *
       * @param {boolean} isolate If true, then the scope does not prototypically inherit from the
       *         parent scope. The scope is isolated, as it can not see parent scope properties.
       *         When creating widgets, it is useful for the widget to not accidentally read parent
       *         state.
       *
       * @param {Scope} [parent=this] The {@link ng.$rootScope.Scope `Scope`} that will be the `$parent`
       *                              of the newly created scope. Defaults to `this` scope if not provided.
       *                              This is used when creating a transclude scope to correctly place it
       *                              in the scope hierarchy while maintaining the correct prototypical
       *                              inheritance.
       *
       * @returns {Object} The newly created child scope.
       *
       */
      $new: function(isolate, parent) {
        var child;

        parent = parent || this;

        if (isolate) {
          child = new Scope();
          child.$root = this.$root;
        } else {
          // Only create a child scope class if somebody asks for one,
          // but cache it to allow the VM to optimize lookups.
          if (!this.$$ChildScope) {
            this.$$ChildScope = createChildScopeClass(this);
          }
          child = new this.$$ChildScope();
        }
        child.$parent = parent;
        child.$$prevSibling = parent.$$childTail;
        if (parent.$$childHead) {
          parent.$$childTail.$$nextSibling = child;
          parent.$$childTail = child;
        } else {
          parent.$$childHead = parent.$$childTail = child;
        }

        // When the new scope is not isolated or we inherit from `this`, and
        // the parent scope is destroyed, the property `$$destroyed` is inherited
        // prototypically. In all other cases, this property needs to be set
        // when the parent scope is destroyed.
        // The listener needs to be added after the parent is set
        if (isolate || parent !== this) child.$on('$destroy', destroyChildScope);

        return child;
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watch
       * @kind function
       *
       * @description
       * Registers a `listener` callback to be executed whenever the `watchExpression` changes.
       *
       * - The `watchExpression` is called on every call to {@link ng.$rootScope.Scope#$digest
       *   $digest()} and should return the value that will be watched. (`watchExpression` should not change
       *   its value when executed multiple times with the same input because it may be executed multiple
       *   times by {@link ng.$rootScope.Scope#$digest $digest()}. That is, `watchExpression` should be
       *   [idempotent](http://en.wikipedia.org/wiki/Idempotence).)
       * - The `listener` is called only when the value from the current `watchExpression` and the
       *   previous call to `watchExpression` are not equal (with the exception of the initial run,
       *   see below). Inequality is determined according to reference inequality,
       *   [strict comparison](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Comparison_Operators)
       *    via the `!==` Javascript operator, unless `objectEquality == true`
       *   (see next point)
       * - When `objectEquality == true`, inequality of the `watchExpression` is determined
       *   according to the {@link angular.equals} function. To save the value of the object for
       *   later comparison, the {@link angular.copy} function is used. This therefore means that
       *   watching complex objects will have adverse memory and performance implications.
       * - This should not be used to watch for changes in objects that are
       *   or contain [File](https://developer.mozilla.org/docs/Web/API/File) objects due to limitations with {@link angular.copy `angular.copy`}.
       * - The watch `listener` may change the model, which may trigger other `listener`s to fire.
       *   This is achieved by rerunning the watchers until no changes are detected. The rerun
       *   iteration limit is 10 to prevent an infinite loop deadlock.
       *
       *
       * If you want to be notified whenever {@link ng.$rootScope.Scope#$digest $digest} is called,
       * you can register a `watchExpression` function with no `listener`. (Be prepared for
       * multiple calls to your `watchExpression` because it will execute multiple times in a
       * single {@link ng.$rootScope.Scope#$digest $digest} cycle if a change is detected.)
       *
       * After a watcher is registered with the scope, the `listener` fn is called asynchronously
       * (via {@link ng.$rootScope.Scope#$evalAsync $evalAsync}) to initialize the
       * watcher. In rare cases, this is undesirable because the listener is called when the result
       * of `watchExpression` didn't change. To detect this scenario within the `listener` fn, you
       * can compare the `newVal` and `oldVal`. If these two values are identical (`===`) then the
       * listener was called due to initialization.
       *
       *
       *
       * # Example
       * ```js
           // let's assume that scope was dependency injected as the $rootScope
           var scope = $rootScope;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);



           // Using a function as a watchExpression
           var food;
           scope.foodCounter = 0;
           expect(scope.foodCounter).toEqual(0);
           scope.$watch(
             // This function returns the value being watched. It is called for each turn of the $digest loop
             function() { return food; },
             // This is the change listener, called when the value returned from the above function changes
             function(newValue, oldValue) {
               if ( newValue !== oldValue ) {
                 // Only increment the counter if the value changed
                 scope.foodCounter = scope.foodCounter + 1;
               }
             }
           );
           // No digest has been run so the counter will be zero
           expect(scope.foodCounter).toEqual(0);

           // Run the digest but since food has not changed count will still be zero
           scope.$digest();
           expect(scope.foodCounter).toEqual(0);

           // Update food and run digest.  Now the counter will increment
           food = 'cheeseburger';
           scope.$digest();
           expect(scope.foodCounter).toEqual(1);

       * ```
       *
       *
       *
       * @param {(function()|string)} watchExpression Expression that is evaluated on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. A change in the return value triggers
       *    a call to the `listener`.
       *
       *    - `string`: Evaluated as {@link guide/expression expression}
       *    - `function(scope)`: called with current `scope` as a parameter.
       * @param {function(newVal, oldVal, scope)} listener Callback called whenever the value
       *    of `watchExpression` changes.
       *
       *    - `newVal` contains the current value of the `watchExpression`
       *    - `oldVal` contains the previous value of the `watchExpression`
       *    - `scope` refers to the current scope
       * @param {boolean=} [objectEquality=false] Compare for object equality using {@link angular.equals} instead of
       *     comparing for reference equality.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $watch: function(watchExp, listener, objectEquality, prettyPrintExpression) {
        var get = $parse(watchExp);

        if (get.$$watchDelegate) {
          return get.$$watchDelegate(this, listener, objectEquality, get, watchExp);
        }
        var scope = this,
            array = scope.$$watchers,
            watcher = {
              fn: listener,
              last: initWatchVal,
              get: get,
              exp: prettyPrintExpression || watchExp,
              eq: !!objectEquality
            };

        lastDirtyWatch = null;

        if (!isFunction(listener)) {
          watcher.fn = noop;
        }

        if (!array) {
          array = scope.$$watchers = [];
          array.$$digestWatchIndex = -1;
        }
        // we use unshift since we use a while loop in $digest for speed.
        // the while loop reads in reverse order.
        array.unshift(watcher);
        array.$$digestWatchIndex++;
        incrementWatchersCount(this, 1);

        return function deregisterWatch() {
          var index = arrayRemove(array, watcher);
          if (index >= 0) {
            incrementWatchersCount(scope, -1);
            if (index < array.$$digestWatchIndex) {
              array.$$digestWatchIndex--;
            }
          }
          lastDirtyWatch = null;
        };
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchGroup
       * @kind function
       *
       * @description
       * A variant of {@link ng.$rootScope.Scope#$watch $watch()} where it watches an array of `watchExpressions`.
       * If any one expression in the collection changes the `listener` is executed.
       *
       * - The items in the `watchExpressions` array are observed via the standard `$watch` operation. Their return
       *   values are examined for changes on every call to `$digest`.
       * - The `listener` is called whenever any expression in the `watchExpressions` array changes.
       *
       * `$watchGroup` is more performant than watching each expression individually, and should be
       * used when the listener does not need to know which expression has changed.
       * If the listener needs to know which expression has changed,
       * {@link ng.$rootScope.Scope#$watch $watch()} or
       * {@link ng.$rootScope.Scope#$watchCollection $watchCollection()} should be used.
       *
       * @param {Array.<string|Function(scope)>} watchExpressions Array of expressions that will be individually
       * watched using {@link ng.$rootScope.Scope#$watch $watch()}
       *
       * @param {function(newValues, oldValues, scope)} listener Callback called whenever the return value of any
       *    expression in `watchExpressions` changes
       *    The `newValues` array contains the current values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`
       *    and the `oldValues` array contains the previous values of the `watchExpressions`, with the indexes matching
       *    those of `watchExpression`.
       *
       *    Note that `newValues` and `oldValues` reflect the differences in each **individual**
       *    expression, and not the difference of the values between each call of the listener.
       *    That means the difference between `newValues` and `oldValues` cannot be used to determine
       *    which expression has changed / remained stable:
       *
       *    ```js
       *
       *    $scope.$watchGroup(['v1', 'v2'], function(newValues, oldValues) {
       *      console.log(newValues, oldValues);
       *    });
       *
       *    // newValues, oldValues initially
       *    // [undefined, undefined], [undefined, undefined]
       *
       *    $scope.v1 = 'a';
       *    $scope.v2 = 'a';
       *
       *    // ['a', 'a'], [undefined, undefined]
       *
       *    $scope.v2 = 'b'
       *
       *    // v1 hasn't changed since it became `'a'`, therefore its oldValue is still `undefined`
       *    // ['a', 'b'], [undefined, 'a']
       *
       *    ```
       *
       *    The `scope` refers to the current scope.
       * @returns {function()} Returns a de-registration function for all listeners.
       */
      $watchGroup: function(watchExpressions, listener) {
        var oldValues = new Array(watchExpressions.length);
        var newValues = new Array(watchExpressions.length);
        var deregisterFns = [];
        var self = this;
        var changeReactionScheduled = false;
        var firstRun = true;

        if (!watchExpressions.length) {
          // No expressions means we call the listener ASAP
          var shouldCall = true;
          self.$evalAsync(function() {
            if (shouldCall) listener(newValues, newValues, self);
          });
          return function deregisterWatchGroup() {
            shouldCall = false;
          };
        }

        if (watchExpressions.length === 1) {
          // Special case size of one
          return this.$watch(watchExpressions[0], function watchGroupAction(value, oldValue, scope) {
            newValues[0] = value;
            oldValues[0] = oldValue;
            listener(newValues, (value === oldValue) ? newValues : oldValues, scope);
          });
        }

        forEach(watchExpressions, function(expr, i) {
          var unwatchFn = self.$watch(expr, function watchGroupSubAction(value, oldValue) {
            newValues[i] = value;
            oldValues[i] = oldValue;
            if (!changeReactionScheduled) {
              changeReactionScheduled = true;
              self.$evalAsync(watchGroupAction);
            }
          });
          deregisterFns.push(unwatchFn);
        });

        function watchGroupAction() {
          changeReactionScheduled = false;

          if (firstRun) {
            firstRun = false;
            listener(newValues, newValues, self);
          } else {
            listener(newValues, oldValues, self);
          }
        }

        return function deregisterWatchGroup() {
          while (deregisterFns.length) {
            deregisterFns.shift()();
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$watchCollection
       * @kind function
       *
       * @description
       * Shallow watches the properties of an object and fires whenever any of the properties change
       * (for arrays, this implies watching the array items; for object maps, this implies watching
       * the properties). If a change is detected, the `listener` callback is fired.
       *
       * - The `obj` collection is observed via standard $watch operation and is examined on every
       *   call to $digest() to see if any items have been added, removed, or moved.
       * - The `listener` is called whenever anything within the `obj` has changed. Examples include
       *   adding, removing, and moving items belonging to an object or array.
       *
       *
       * # Example
       * ```js
          $scope.names = ['igor', 'matias', 'misko', 'james'];
          $scope.dataCount = 4;

          $scope.$watchCollection('names', function(newNames, oldNames) {
            $scope.dataCount = newNames.length;
          });

          expect($scope.dataCount).toEqual(4);
          $scope.$digest();

          //still at 4 ... no changes
          expect($scope.dataCount).toEqual(4);

          $scope.names.pop();
          $scope.$digest();

          //now there's been a change
          expect($scope.dataCount).toEqual(3);
       * ```
       *
       *
       * @param {string|function(scope)} obj Evaluated as {@link guide/expression expression}. The
       *    expression value should evaluate to an object or an array which is observed on each
       *    {@link ng.$rootScope.Scope#$digest $digest} cycle. Any shallow change within the
       *    collection will trigger a call to the `listener`.
       *
       * @param {function(newCollection, oldCollection, scope)} listener a callback function called
       *    when a change is detected.
       *    - The `newCollection` object is the newly modified data obtained from the `obj` expression
       *    - The `oldCollection` object is a copy of the former collection data.
       *      Due to performance considerations, the`oldCollection` value is computed only if the
       *      `listener` function declares two or more arguments.
       *    - The `scope` argument refers to the current scope.
       *
       * @returns {function()} Returns a de-registration function for this listener. When the
       *    de-registration function is executed, the internal watch operation is terminated.
       */
      $watchCollection: function(obj, listener) {
        $watchCollectionInterceptor.$stateful = true;

        var self = this;
        // the current value, updated on each dirty-check run
        var newValue;
        // a shallow copy of the newValue from the last dirty-check run,
        // updated to match newValue during dirty-check run
        var oldValue;
        // a shallow copy of the newValue from when the last change happened
        var veryOldValue;
        // only track veryOldValue if the listener is asking for it
        var trackVeryOldValue = (listener.length > 1);
        var changeDetected = 0;
        var changeDetector = $parse(obj, $watchCollectionInterceptor);
        var internalArray = [];
        var internalObject = {};
        var initRun = true;
        var oldLength = 0;

        function $watchCollectionInterceptor(_value) {
          newValue = _value;
          var newLength, key, bothNaN, newItem, oldItem;

          // If the new value is undefined, then return undefined as the watch may be a one-time watch
          if (isUndefined(newValue)) return;

          if (!isObject(newValue)) { // if primitive
            if (oldValue !== newValue) {
              oldValue = newValue;
              changeDetected++;
            }
          } else if (isArrayLike(newValue)) {
            if (oldValue !== internalArray) {
              // we are transitioning from something which was not an array into array.
              oldValue = internalArray;
              oldLength = oldValue.length = 0;
              changeDetected++;
            }

            newLength = newValue.length;

            if (oldLength !== newLength) {
              // if lengths do not match we need to trigger change notification
              changeDetected++;
              oldValue.length = oldLength = newLength;
            }
            // copy the items to oldValue and look for changes.
            for (var i = 0; i < newLength; i++) {
              oldItem = oldValue[i];
              newItem = newValue[i];

              // eslint-disable-next-line no-self-compare
              bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
              if (!bothNaN && (oldItem !== newItem)) {
                changeDetected++;
                oldValue[i] = newItem;
              }
            }
          } else {
            if (oldValue !== internalObject) {
              // we are transitioning from something which was not an object into object.
              oldValue = internalObject = {};
              oldLength = 0;
              changeDetected++;
            }
            // copy the items to oldValue and look for changes.
            newLength = 0;
            for (key in newValue) {
              if (hasOwnProperty.call(newValue, key)) {
                newLength++;
                newItem = newValue[key];
                oldItem = oldValue[key];

                if (key in oldValue) {
                  // eslint-disable-next-line no-self-compare
                  bothNaN = (oldItem !== oldItem) && (newItem !== newItem);
                  if (!bothNaN && (oldItem !== newItem)) {
                    changeDetected++;
                    oldValue[key] = newItem;
                  }
                } else {
                  oldLength++;
                  oldValue[key] = newItem;
                  changeDetected++;
                }
              }
            }
            if (oldLength > newLength) {
              // we used to have more keys, need to find them and destroy them.
              changeDetected++;
              for (key in oldValue) {
                if (!hasOwnProperty.call(newValue, key)) {
                  oldLength--;
                  delete oldValue[key];
                }
              }
            }
          }
          return changeDetected;
        }

        function $watchCollectionAction() {
          if (initRun) {
            initRun = false;
            listener(newValue, newValue, self);
          } else {
            listener(newValue, veryOldValue, self);
          }

          // make a copy for the next time a collection is changed
          if (trackVeryOldValue) {
            if (!isObject(newValue)) {
              //primitive
              veryOldValue = newValue;
            } else if (isArrayLike(newValue)) {
              veryOldValue = new Array(newValue.length);
              for (var i = 0; i < newValue.length; i++) {
                veryOldValue[i] = newValue[i];
              }
            } else { // if object
              veryOldValue = {};
              for (var key in newValue) {
                if (hasOwnProperty.call(newValue, key)) {
                  veryOldValue[key] = newValue[key];
                }
              }
            }
          }
        }

        return this.$watch(changeDetector, $watchCollectionAction);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$digest
       * @kind function
       *
       * @description
       * Processes all of the {@link ng.$rootScope.Scope#$watch watchers} of the current scope and
       * its children. Because a {@link ng.$rootScope.Scope#$watch watcher}'s listener can change
       * the model, the `$digest()` keeps calling the {@link ng.$rootScope.Scope#$watch watchers}
       * until no more listeners are firing. This means that it is possible to get into an infinite
       * loop. This function will throw `'Maximum iteration limit exceeded.'` if the number of
       * iterations exceeds 10.
       *
       * Usually, you don't call `$digest()` directly in
       * {@link ng.directive:ngController controllers} or in
       * {@link ng.$compileProvider#directive directives}.
       * Instead, you should call {@link ng.$rootScope.Scope#$apply $apply()} (typically from within
       * a {@link ng.$compileProvider#directive directive}), which will force a `$digest()`.
       *
       * If you want to be notified whenever `$digest()` is called,
       * you can register a `watchExpression` function with
       * {@link ng.$rootScope.Scope#$watch $watch()} with no `listener`.
       *
       * In unit tests, you may need to call `$digest()` to simulate the scope life cycle.
       *
       * # Example
       * ```js
           var scope = ...;
           scope.name = 'misko';
           scope.counter = 0;

           expect(scope.counter).toEqual(0);
           scope.$watch('name', function(newValue, oldValue) {
             scope.counter = scope.counter + 1;
           });
           expect(scope.counter).toEqual(0);

           scope.$digest();
           // the listener is always called during the first $digest loop after it was registered
           expect(scope.counter).toEqual(1);

           scope.$digest();
           // but now it will not be called unless the value changes
           expect(scope.counter).toEqual(1);

           scope.name = 'adam';
           scope.$digest();
           expect(scope.counter).toEqual(2);
       * ```
       *
       */
      $digest: function() {
        var watch, value, last, fn, get,
            watchers,
            dirty, ttl = TTL,
            next, current, target = this,
            watchLog = [],
            logIdx, asyncTask;

        beginPhase('$digest');
        // Check for changes to browser url that happened in sync before the call to $digest
        $browser.$$checkUrlChange();

        if (this === $rootScope && applyAsyncId !== null) {
          // If this is the root scope, and $applyAsync has scheduled a deferred $apply(), then
          // cancel the scheduled $apply and flush the queue of expressions to be evaluated.
          $browser.defer.cancel(applyAsyncId);
          flushApplyAsync();
        }

        lastDirtyWatch = null;

        do { // "while dirty" loop
          dirty = false;
          current = target;

          // It's safe for asyncQueuePosition to be a local variable here because this loop can't
          // be reentered recursively. Calling $digest from a function passed to $evalAsync would
          // lead to a '$digest already in progress' error.
          for (var asyncQueuePosition = 0; asyncQueuePosition < asyncQueue.length; asyncQueuePosition++) {
            try {
              asyncTask = asyncQueue[asyncQueuePosition];
              fn = asyncTask.fn;
              fn(asyncTask.scope, asyncTask.locals);
            } catch (e) {
              $exceptionHandler(e);
            }
            lastDirtyWatch = null;
          }
          asyncQueue.length = 0;

          traverseScopesLoop:
          do { // "traverse the scopes" loop
            if ((watchers = current.$$watchers)) {
              // process our watches
              watchers.$$digestWatchIndex = watchers.length;
              while (watchers.$$digestWatchIndex--) {
                try {
                  watch = watchers[watchers.$$digestWatchIndex];
                  // Most common watches are on primitives, in which case we can short
                  // circuit it with === operator, only when === fails do we use .equals
                  if (watch) {
                    get = watch.get;
                    if ((value = get(current)) !== (last = watch.last) &&
                        !(watch.eq
                            ? equals(value, last)
                            : (isNumberNaN(value) && isNumberNaN(last)))) {
                      dirty = true;
                      lastDirtyWatch = watch;
                      watch.last = watch.eq ? copy(value, null) : value;
                      fn = watch.fn;
                      fn(value, ((last === initWatchVal) ? value : last), current);
                      if (ttl < 5) {
                        logIdx = 4 - ttl;
                        if (!watchLog[logIdx]) watchLog[logIdx] = [];
                        watchLog[logIdx].push({
                          msg: isFunction(watch.exp) ? 'fn: ' + (watch.exp.name || watch.exp.toString()) : watch.exp,
                          newVal: value,
                          oldVal: last
                        });
                      }
                    } else if (watch === lastDirtyWatch) {
                      // If the most recently dirty watcher is now clean, short circuit since the remaining watchers
                      // have already been tested.
                      dirty = false;
                      break traverseScopesLoop;
                    }
                  }
                } catch (e) {
                  $exceptionHandler(e);
                }
              }
            }

            // Insanity Warning: scope depth-first traversal
            // yes, this code is a bit crazy, but it works and we have tests to prove it!
            // this piece should be kept in sync with the traversal in $broadcast
            if (!(next = ((current.$$watchersCount && current.$$childHead) ||
                (current !== target && current.$$nextSibling)))) {
              while (current !== target && !(next = current.$$nextSibling)) {
                current = current.$parent;
              }
            }
          } while ((current = next));

          // `break traverseScopesLoop;` takes us to here

          if ((dirty || asyncQueue.length) && !(ttl--)) {
            clearPhase();
            throw $rootScopeMinErr('infdig',
                '{0} $digest() iterations reached. Aborting!\n' +
                'Watchers fired in the last 5 iterations: {1}',
                TTL, watchLog);
          }

        } while (dirty || asyncQueue.length);

        clearPhase();

        // postDigestQueuePosition isn't local here because this loop can be reentered recursively.
        while (postDigestQueuePosition < postDigestQueue.length) {
          try {
            postDigestQueue[postDigestQueuePosition++]();
          } catch (e) {
            $exceptionHandler(e);
          }
        }
        postDigestQueue.length = postDigestQueuePosition = 0;

        // Check for changes to browser url that happened during the $digest
        // (for which no event is fired; e.g. via `history.pushState()`)
        $browser.$$checkUrlChange();
      },


      /**
       * @ngdoc event
       * @name $rootScope.Scope#$destroy
       * @eventType broadcast on scope being destroyed
       *
       * @description
       * Broadcasted when a scope and its children are being destroyed.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$destroy
       * @kind function
       *
       * @description
       * Removes the current scope (and all of its children) from the parent scope. Removal implies
       * that calls to {@link ng.$rootScope.Scope#$digest $digest()} will no longer
       * propagate to the current scope and its children. Removal also implies that the current
       * scope is eligible for garbage collection.
       *
       * The `$destroy()` is usually used by directives such as
       * {@link ng.directive:ngRepeat ngRepeat} for managing the
       * unrolling of the loop.
       *
       * Just before a scope is destroyed, a `$destroy` event is broadcasted on this scope.
       * Application code can register a `$destroy` event handler that will give it a chance to
       * perform any necessary cleanup.
       *
       * Note that, in AngularJS, there is also a `$destroy` jQuery event, which can be used to
       * clean up DOM bindings before an element is removed from the DOM.
       */
      $destroy: function() {
        // We can't destroy a scope that has been already destroyed.
        if (this.$$destroyed) return;
        var parent = this.$parent;

        this.$broadcast('$destroy');
        this.$$destroyed = true;

        if (this === $rootScope) {
          //Remove handlers attached to window when $rootScope is removed
          $browser.$$applicationDestroyed();
        }

        incrementWatchersCount(this, -this.$$watchersCount);
        for (var eventName in this.$$listenerCount) {
          decrementListenerCount(this, this.$$listenerCount[eventName], eventName);
        }

        // sever all the references to parent scopes (after this cleanup, the current scope should
        // not be retained by any of our references and should be eligible for garbage collection)
        if (parent && parent.$$childHead === this) parent.$$childHead = this.$$nextSibling;
        if (parent && parent.$$childTail === this) parent.$$childTail = this.$$prevSibling;
        if (this.$$prevSibling) this.$$prevSibling.$$nextSibling = this.$$nextSibling;
        if (this.$$nextSibling) this.$$nextSibling.$$prevSibling = this.$$prevSibling;

        // Disable listeners, watchers and apply/digest methods
        this.$destroy = this.$digest = this.$apply = this.$evalAsync = this.$applyAsync = noop;
        this.$on = this.$watch = this.$watchGroup = function() { return noop; };
        this.$$listeners = {};

        // Disconnect the next sibling to prevent `cleanUpScope` destroying those too
        this.$$nextSibling = null;
        cleanUpScope(this);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$eval
       * @kind function
       *
       * @description
       * Executes the `expression` on the current scope and returns the result. Any exceptions in
       * the expression are propagated (uncaught). This is useful when evaluating Angular
       * expressions.
       *
       * # Example
       * ```js
           var scope = ng.$rootScope.Scope();
           scope.a = 1;
           scope.b = 2;

           expect(scope.$eval('a+b')).toEqual(3);
           expect(scope.$eval(function(scope){ return scope.a + scope.b; })).toEqual(3);
       * ```
       *
       * @param {(string|function())=} expression An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in  {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       * @returns {*} The result of evaluating the expression.
       */
      $eval: function(expr, locals) {
        return $parse(expr)(this, locals);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$evalAsync
       * @kind function
       *
       * @description
       * Executes the expression on the current scope at a later point in time.
       *
       * The `$evalAsync` makes no guarantees as to when the `expression` will be executed, only
       * that:
       *
       *   - it will execute after the function that scheduled the evaluation (preferably before DOM
       *     rendering).
       *   - at least one {@link ng.$rootScope.Scope#$digest $digest cycle} will be performed after
       *     `expression` execution.
       *
       * Any exceptions from the execution of the expression are forwarded to the
       * {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * __Note:__ if this function is called outside of a `$digest` cycle, a new `$digest` cycle
       * will be scheduled. However, it is encouraged to always call code that changes the model
       * from within an `$apply` call. That includes code evaluated via `$evalAsync`.
       *
       * @param {(string|function())=} expression An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with the current `scope` parameter.
       *
       * @param {(object)=} locals Local variables object, useful for overriding values in scope.
       */
      $evalAsync: function(expr, locals) {
        // if we are outside of an $digest loop and this is the first time we are scheduling async
        // task also schedule async auto-flush
        if (!$rootScope.$$phase && !asyncQueue.length) {
          $browser.defer(function() {
            if (asyncQueue.length) {
              $rootScope.$digest();
            }
          });
        }

        asyncQueue.push({scope: this, fn: $parse(expr), locals: locals});
      },

      $$postDigest: function(fn) {
        postDigestQueue.push(fn);
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$apply
       * @kind function
       *
       * @description
       * `$apply()` is used to execute an expression in angular from outside of the angular
       * framework. (For example from browser DOM events, setTimeout, XHR or third party libraries).
       * Because we are calling into the angular framework we need to perform proper scope life
       * cycle of {@link ng.$exceptionHandler exception handling},
       * {@link ng.$rootScope.Scope#$digest executing watches}.
       *
       * ## Life cycle
       *
       * # Pseudo-Code of `$apply()`
       * ```js
           function $apply(expr) {
             try {
               return $eval(expr);
             } catch (e) {
               $exceptionHandler(e);
             } finally {
               $root.$digest();
             }
           }
       * ```
       *
       *
       * Scope's `$apply()` method transitions through the following stages:
       *
       * 1. The {@link guide/expression expression} is executed using the
       *    {@link ng.$rootScope.Scope#$eval $eval()} method.
       * 2. Any exceptions from the execution of the expression are forwarded to the
       *    {@link ng.$exceptionHandler $exceptionHandler} service.
       * 3. The {@link ng.$rootScope.Scope#$watch watch} listeners are fired immediately after the
       *    expression was executed using the {@link ng.$rootScope.Scope#$digest $digest()} method.
       *
       *
       * @param {(string|function())=} exp An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       *
       * @returns {*} The result of evaluating the expression.
       */
      $apply: function(expr) {
        try {
          beginPhase('$apply');
          try {
            return this.$eval(expr);
          } finally {
            clearPhase();
          }
        } catch (e) {
          $exceptionHandler(e);
        } finally {
          try {
            $rootScope.$digest();
          } catch (e) {
            $exceptionHandler(e);
            // eslint-disable-next-line no-unsafe-finally
            throw e;
          }
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$applyAsync
       * @kind function
       *
       * @description
       * Schedule the invocation of $apply to occur at a later time. The actual time difference
       * varies across browsers, but is typically around ~10 milliseconds.
       *
       * This can be used to queue up multiple expressions which need to be evaluated in the same
       * digest.
       *
       * @param {(string|function())=} exp An angular expression to be executed.
       *
       *    - `string`: execute using the rules as defined in {@link guide/expression expression}.
       *    - `function(scope)`: execute the function with current `scope` parameter.
       */
      $applyAsync: function(expr) {
        var scope = this;
        if (expr) {
          applyAsyncQueue.push($applyAsyncExpression);
        }
        expr = $parse(expr);
        scheduleApplyAsync();

        function $applyAsyncExpression() {
          scope.$eval(expr);
        }
      },

      /**
       * @ngdoc method
       * @name $rootScope.Scope#$on
       * @kind function
       *
       * @description
       * Listens on events of a given type. See {@link ng.$rootScope.Scope#$emit $emit} for
       * discussion of event life cycle.
       *
       * The event listener function format is: `function(event, args...)`. The `event` object
       * passed into the listener has the following attributes:
       *
       *   - `targetScope` - `{Scope}`: the scope on which the event was `$emit`-ed or
       *     `$broadcast`-ed.
       *   - `currentScope` - `{Scope}`: the scope that is currently handling the event. Once the
       *     event propagates through the scope hierarchy, this property is set to null.
       *   - `name` - `{string}`: name of the event.
       *   - `stopPropagation` - `{function=}`: calling `stopPropagation` function will cancel
       *     further event propagation (available only for events that were `$emit`-ed).
       *   - `preventDefault` - `{function}`: calling `preventDefault` sets `defaultPrevented` flag
       *     to true.
       *   - `defaultPrevented` - `{boolean}`: true if `preventDefault` was called.
       *
       * @param {string} name Event name to listen on.
       * @param {function(event, ...args)} listener Function to call when the event is emitted.
       * @returns {function()} Returns a deregistration function for this listener.
       */
      $on: function(name, listener) {
        var namedListeners = this.$$listeners[name];
        if (!namedListeners) {
          this.$$listeners[name] = namedListeners = [];
        }
        namedListeners.push(listener);

        var current = this;
        do {
          if (!current.$$listenerCount[name]) {
            current.$$listenerCount[name] = 0;
          }
          current.$$listenerCount[name]++;
        } while ((current = current.$parent));

        var self = this;
        return function() {
          var indexOfListener = namedListeners.indexOf(listener);
          if (indexOfListener !== -1) {
            namedListeners[indexOfListener] = null;
            decrementListenerCount(self, 1, name);
          }
        };
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$emit
       * @kind function
       *
       * @description
       * Dispatches an event `name` upwards through the scope hierarchy notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$emit` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event traverses upwards toward the root scope and calls all
       * registered listeners along the way. The event will stop propagating if one of the listeners
       * cancels it.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to emit.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object (see {@link ng.$rootScope.Scope#$on}).
       */
      $emit: function(name, args) {
        var empty = [],
            namedListeners,
            scope = this,
            stopPropagation = false,
            event = {
              name: name,
              targetScope: scope,
              stopPropagation: function() {stopPropagation = true;},
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            },
            listenerArgs = concat([event], arguments, 1),
            i, length;

        do {
          namedListeners = scope.$$listeners[name] || empty;
          event.currentScope = scope;
          for (i = 0, length = namedListeners.length; i < length; i++) {

            // if listeners were deregistered, defragment the array
            if (!namedListeners[i]) {
              namedListeners.splice(i, 1);
              i--;
              length--;
              continue;
            }
            try {
              //allow all listeners attached to the current scope to run
              namedListeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }
          //if any listener on the current scope stops propagation, prevent bubbling
          if (stopPropagation) {
            event.currentScope = null;
            return event;
          }
          //traverse upwards
          scope = scope.$parent;
        } while (scope);

        event.currentScope = null;

        return event;
      },


      /**
       * @ngdoc method
       * @name $rootScope.Scope#$broadcast
       * @kind function
       *
       * @description
       * Dispatches an event `name` downwards to all child scopes (and their children) notifying the
       * registered {@link ng.$rootScope.Scope#$on} listeners.
       *
       * The event life cycle starts at the scope on which `$broadcast` was called. All
       * {@link ng.$rootScope.Scope#$on listeners} listening for `name` event on this scope get
       * notified. Afterwards, the event propagates to all direct and indirect scopes of the current
       * scope and calls all registered listeners along the way. The event cannot be canceled.
       *
       * Any exception emitted from the {@link ng.$rootScope.Scope#$on listeners} will be passed
       * onto the {@link ng.$exceptionHandler $exceptionHandler} service.
       *
       * @param {string} name Event name to broadcast.
       * @param {...*} args Optional one or more arguments which will be passed onto the event listeners.
       * @return {Object} Event object, see {@link ng.$rootScope.Scope#$on}
       */
      $broadcast: function(name, args) {
        var target = this,
            current = target,
            next = target,
            event = {
              name: name,
              targetScope: target,
              preventDefault: function() {
                event.defaultPrevented = true;
              },
              defaultPrevented: false
            };

        if (!target.$$listenerCount[name]) return event;

        var listenerArgs = concat([event], arguments, 1),
            listeners, i, length;

        //down while you can, then up and next sibling or up and next sibling until back at root
        while ((current = next)) {
          event.currentScope = current;
          listeners = current.$$listeners[name] || [];
          for (i = 0, length = listeners.length; i < length; i++) {
            // if listeners were deregistered, defragment the array
            if (!listeners[i]) {
              listeners.splice(i, 1);
              i--;
              length--;
              continue;
            }

            try {
              listeners[i].apply(null, listenerArgs);
            } catch (e) {
              $exceptionHandler(e);
            }
          }

          // Insanity Warning: scope depth-first traversal
          // yes, this code is a bit crazy, but it works and we have tests to prove it!
          // this piece should be kept in sync with the traversal in $digest
          // (though it differs due to having the extra check for $$listenerCount)
          if (!(next = ((current.$$listenerCount[name] && current.$$childHead) ||
              (current !== target && current.$$nextSibling)))) {
            while (current !== target && !(next = current.$$nextSibling)) {
              current = current.$parent;
            }
          }
        }

        event.currentScope = null;
        return event;
      }
    };

    var $rootScope = new Scope();

    //The internal queues. Expose them on the $rootScope for debugging/testing purposes.
    var asyncQueue = $rootScope.$$asyncQueue = [];
    var postDigestQueue = $rootScope.$$postDigestQueue = [];
    var applyAsyncQueue = $rootScope.$$applyAsyncQueue = [];

    var postDigestQueuePosition = 0;

    return $rootScope;


    function beginPhase(phase) {
      if ($rootScope.$$phase) {
        throw $rootScopeMinErr('inprog', '{0} already in progress', $rootScope.$$phase);
      }

      $rootScope.$$phase = phase;
    }

    function clearPhase() {
      $rootScope.$$phase = null;
    }

    function incrementWatchersCount(current, count) {
      do {
        current.$$watchersCount += count;
      } while ((current = current.$parent));
    }

    function decrementListenerCount(current, count, name) {
      do {
        current.$$listenerCount[name] -= count;

        if (current.$$listenerCount[name] === 0) {
          delete current.$$listenerCount[name];
        }
      } while ((current = current.$parent));
    }

    /**
     * function used as an initial value for watchers.
     * because it's unique we can easily tell it apart from other values
     */
    function initWatchVal() {}

    function flushApplyAsync() {
      while (applyAsyncQueue.length) {
        try {
          applyAsyncQueue.shift()();
        } catch (e) {
          $exceptionHandler(e);
        }
      }
      applyAsyncId = null;
    }

    function scheduleApplyAsync() {
      if (applyAsyncId === null) {
        applyAsyncId = $browser.defer(function() {
          $rootScope.$apply(flushApplyAsync);
        });
      }
    }
  }];
}

/**
 * @ngdoc service
 * @name $rootElement
 *
 * @description
 * The root element of Angular application. This is either the element where {@link
 * ng.directive:ngApp ngApp} was declared or the element passed into
 * {@link angular.bootstrap}. The element represents the root element of application. It is also the
 * location where the application's {@link auto.$injector $injector} service gets
 * published, and can be retrieved using `$rootElement.injector()`.
 */


// the implementation is in angular.bootstrap

/**
 * @this
 * @description
 * Private service to sanitize uris for links and images. Used by $compile and $sanitize.
 */
function $$SanitizeUriProvider() {
  var aHrefSanitizationWhitelist = /^\s*(https?|ftp|mailto|tel|file):/,
    imgSrcSanitizationWhitelist = /^\s*((https?|ftp|file|blob):|data:image\/)/;

  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during a[href] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to a[href] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `aHrefSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.aHrefSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      aHrefSanitizationWhitelist = regexp;
      return this;
    }
    return aHrefSanitizationWhitelist;
  };


  /**
   * @description
   * Retrieves or overrides the default regular expression that is used for whitelisting of safe
   * urls during img[src] sanitization.
   *
   * The sanitization is a security measure aimed at prevent XSS attacks via html links.
   *
   * Any url about to be assigned to img[src] via data-binding is first normalized and turned into
   * an absolute url. Afterwards, the url is matched against the `imgSrcSanitizationWhitelist`
   * regular expression. If a match is found, the original url is written into the dom. Otherwise,
   * the absolute url is prefixed with `'unsafe:'` string and only then is it written into the DOM.
   *
   * @param {RegExp=} regexp New regexp to whitelist urls with.
   * @returns {RegExp|ng.$compileProvider} Current RegExp if called without value or self for
   *    chaining otherwise.
   */
  this.imgSrcSanitizationWhitelist = function(regexp) {
    if (isDefined(regexp)) {
      imgSrcSanitizationWhitelist = regexp;
      return this;
    }
    return imgSrcSanitizationWhitelist;
  };

  this.$get = function() {
    return function sanitizeUri(uri, isImage) {
      var regex = isImage ? imgSrcSanitizationWhitelist : aHrefSanitizationWhitelist;
      var normalizedVal;
      normalizedVal = urlResolve(uri).href;
      if (normalizedVal !== '' && !normalizedVal.match(regex)) {
        return 'unsafe:' + normalizedVal;
      }
      return uri;
    };
  };
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

/* exported $SceProvider, $SceDelegateProvider */

var $sceMinErr = minErr('$sce');

var SCE_CONTEXTS = {
  // HTML is used when there's HTML rendered (e.g. ng-bind-html, iframe srcdoc binding).
  HTML: 'html',

  // Style statements or stylesheets. Currently unused in AngularJS.
  CSS: 'css',

  // An URL used in a context where it does not refer to a resource that loads code. Currently
  // unused in AngularJS.
  URL: 'url',

  // RESOURCE_URL is a subtype of URL used where the referred-to resource could be interpreted as
  // code. (e.g. ng-include, script src binding, templateUrl)
  RESOURCE_URL: 'resourceUrl',

  // Script. Currently unused in AngularJS.
  JS: 'js'
};

// Helper functions follow.

var UNDERSCORE_LOWERCASE_REGEXP = /_([a-z])/g;

function snakeToCamel(name) {
  return name
    .replace(UNDERSCORE_LOWERCASE_REGEXP, fnCamelCaseReplace);
}

function adjustMatcher(matcher) {
  if (matcher === 'self') {
    return matcher;
  } else if (isString(matcher)) {
    // Strings match exactly except for 2 wildcards - '*' and '**'.
    // '*' matches any character except those from the set ':/.?&'.
    // '**' matches any character (like .* in a RegExp).
    // More than 2 *'s raises an error as it's ill defined.
    if (matcher.indexOf('***') > -1) {
      throw $sceMinErr('iwcard',
          'Illegal sequence *** in string matcher.  String: {0}', matcher);
    }
    matcher = escapeForRegexp(matcher).
                  replace(/\\\*\\\*/g, '.*').
                  replace(/\\\*/g, '[^:/.?&;]*');
    return new RegExp('^' + matcher + '$');
  } else if (isRegExp(matcher)) {
    // The only other type of matcher allowed is a Regexp.
    // Match entire URL / disallow partial matches.
    // Flags are reset (i.e. no global, ignoreCase or multiline)
    return new RegExp('^' + matcher.source + '$');
  } else {
    throw $sceMinErr('imatcher',
        'Matchers may only be "self", string patterns or RegExp objects');
  }
}


function adjustMatchers(matchers) {
  var adjustedMatchers = [];
  if (isDefined(matchers)) {
    forEach(matchers, function(matcher) {
      adjustedMatchers.push(adjustMatcher(matcher));
    });
  }
  return adjustedMatchers;
}


/**
 * @ngdoc service
 * @name $sceDelegate
 * @kind function
 *
 * @description
 *
 * `$sceDelegate` is a service that is used by the `$sce` service to provide {@link ng.$sce Strict
 * Contextual Escaping (SCE)} services to AngularJS.
 *
 * For an overview of this service and the functionnality it provides in AngularJS, see the main
 * page for {@link ng.$sce SCE}. The current page is targeted for developers who need to alter how
 * SCE works in their application, which shouldn't be needed in most cases.
 *
 * <div class="alert alert-danger">
 * AngularJS strongly relies on contextual escaping for the security of bindings: disabling or
 * modifying this might cause cross site scripting (XSS) vulnerabilities. For libraries owners,
 * changes to this service will also influence users, so be extra careful and document your changes.
 * </div>
 *
 * Typically, you would configure or override the {@link ng.$sceDelegate $sceDelegate} instead of
 * the `$sce` service to customize the way Strict Contextual Escaping works in AngularJS.  This is
 * because, while the `$sce` provides numerous shorthand methods, etc., you really only need to
 * override 3 core functions (`trustAs`, `getTrusted` and `valueOf`) to replace the way things
 * work because `$sce` delegates to `$sceDelegate` for these operations.
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} to configure this service.
 *
 * The default instance of `$sceDelegate` should work out of the box with little pain.  While you
 * can override it completely to change the behavior of `$sce`, the common case would
 * involve configuring the {@link ng.$sceDelegateProvider $sceDelegateProvider} instead by setting
 * your own whitelists and blacklists for trusting URLs used for loading AngularJS resources such as
 * templates.  Refer {@link ng.$sceDelegateProvider#resourceUrlWhitelist
 * $sceDelegateProvider.resourceUrlWhitelist} and {@link
 * ng.$sceDelegateProvider#resourceUrlBlacklist $sceDelegateProvider.resourceUrlBlacklist}
 */

/**
 * @ngdoc provider
 * @name $sceDelegateProvider
 * @this
 *
 * @description
 *
 * The `$sceDelegateProvider` provider allows developers to configure the {@link ng.$sceDelegate
 * $sceDelegate service}, used as a delegate for {@link ng.$sce Strict Contextual Escaping (SCE)}.
 *
 * The `$sceDelegateProvider` allows one to get/set the whitelists and blacklists used to ensure
 * that the URLs used for sourcing AngularJS templates and other script-running URLs are safe (all
 * places that use the `$sce.RESOURCE_URL` context). See
 * {@link ng.$sceDelegateProvider#resourceUrlWhitelist $sceDelegateProvider.resourceUrlWhitelist}
 * and
 * {@link ng.$sceDelegateProvider#resourceUrlBlacklist $sceDelegateProvider.resourceUrlBlacklist},
 *
 * For the general details about this service in Angular, read the main page for {@link ng.$sce
 * Strict Contextual Escaping (SCE)}.
 *
 * **Example**:  Consider the following case. <a name="example"></a>
 *
 * - your app is hosted at url `http://myapp.example.com/`
 * - but some of your templates are hosted on other domains you control such as
 *   `http://srv01.assets.example.com/`, `http://srv02.assets.example.com/`, etc.
 * - and you have an open redirect at `http://myapp.example.com/clickThru?...`.
 *
 * Here is what a secure configuration for this scenario might look like:
 *
 * ```
 *  angular.module('myApp', []).config(function($sceDelegateProvider) {
 *    $sceDelegateProvider.resourceUrlWhitelist([
 *      // Allow same origin resource loads.
 *      'self',
 *      // Allow loading from our assets domain.  Notice the difference between * and **.
 *      'http://srv*.assets.example.com/**'
 *    ]);
 *
 *    // The blacklist overrides the whitelist so the open redirect here is blocked.
 *    $sceDelegateProvider.resourceUrlBlacklist([
 *      'http://myapp.example.com/clickThru**'
 *    ]);
 *  });
 * ```
 * Note that an empty whitelist will block every resource URL from being loaded, and will require
 * you to manually mark each one as trusted with `$sce.trustAsResourceUrl`. However, templates
 * requested by {@link ng.$templateRequest $templateRequest} that are present in
 * {@link ng.$templateCache $templateCache} will not go through this check. If you have a mechanism
 * to populate your templates in that cache at config time, then it is a good idea to remove 'self'
 * from that whitelist. This helps to mitigate the security impact of certain types of issues, like
 * for instance attacker-controlled `ng-includes`.
 */

function $SceDelegateProvider() {
  this.SCE_CONTEXTS = SCE_CONTEXTS;

  // Resource URLs can also be trusted by policy.
  var resourceUrlWhitelist = ['self'],
      resourceUrlBlacklist = [];

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlWhitelist
   * @kind function
   *
   * @param {Array=} whitelist When provided, replaces the resourceUrlWhitelist with the value
   *     provided.  This must be an array or null.  A snapshot of this array is used so further
   *     changes to the array are ignored.
   *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *     allowed in this array.
   *
   * @return {Array} The currently set whitelist array.
   *
   * @description
   * Sets/Gets the whitelist of trusted resource URLs.
   *
   * The **default value** when no whitelist has been explicitly set is `['self']` allowing only
   * same origin resource requests.
   *
   * <div class="alert alert-warning">
   * **Note:** the default whitelist of 'self' is not recommended if your app shares its origin
   * with other apps! It is a good idea to limit it to only your application's directory.
   * </div>
   */
  this.resourceUrlWhitelist = function(value) {
    if (arguments.length) {
      resourceUrlWhitelist = adjustMatchers(value);
    }
    return resourceUrlWhitelist;
  };

  /**
   * @ngdoc method
   * @name $sceDelegateProvider#resourceUrlBlacklist
   * @kind function
   *
   * @param {Array=} blacklist When provided, replaces the resourceUrlBlacklist with the value
   *     provided.  This must be an array or null.  A snapshot of this array is used so further
   *     changes to the array are ignored.</p><p>
   *     Follow {@link ng.$sce#resourceUrlPatternItem this link} for a description of the items
   *     allowed in this array.</p><p>
   *     The typical usage for the blacklist is to **block
   *     [open redirects](http://cwe.mitre.org/data/definitions/601.html)** served by your domain as
   *     these would otherwise be trusted but actually return content from the redirected domain.
   *     </p><p>
   *     Finally, **the blacklist overrides the whitelist** and has the final say.
   *
   * @return {Array} The currently set blacklist array.
   *
   * @description
   * Sets/Gets the blacklist of trusted resource URLs.
   *
   * The **default value** when no whitelist has been explicitly set is the empty array (i.e. there
   * is no blacklist.)
   */

  this.resourceUrlBlacklist = function(value) {
    if (arguments.length) {
      resourceUrlBlacklist = adjustMatchers(value);
    }
    return resourceUrlBlacklist;
  };

  this.$get = ['$injector', function($injector) {

    var htmlSanitizer = function htmlSanitizer(html) {
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    };

    if ($injector.has('$sanitize')) {
      htmlSanitizer = $injector.get('$sanitize');
    }


    function matchUrl(matcher, parsedUrl) {
      if (matcher === 'self') {
        return urlIsSameOrigin(parsedUrl);
      } else {
        // definitely a regex.  See adjustMatchers()
        return !!matcher.exec(parsedUrl.href);
      }
    }

    function isResourceUrlAllowedByPolicy(url) {
      var parsedUrl = urlResolve(url.toString());
      var i, n, allowed = false;
      // Ensure that at least one item from the whitelist allows this url.
      for (i = 0, n = resourceUrlWhitelist.length; i < n; i++) {
        if (matchUrl(resourceUrlWhitelist[i], parsedUrl)) {
          allowed = true;
          break;
        }
      }
      if (allowed) {
        // Ensure that no item from the blacklist blocked this url.
        for (i = 0, n = resourceUrlBlacklist.length; i < n; i++) {
          if (matchUrl(resourceUrlBlacklist[i], parsedUrl)) {
            allowed = false;
            break;
          }
        }
      }
      return allowed;
    }

    function generateHolderType(Base) {
      var holderType = function TrustedValueHolderType(trustedValue) {
        this.$$unwrapTrustedValue = function() {
          return trustedValue;
        };
      };
      if (Base) {
        holderType.prototype = new Base();
      }
      holderType.prototype.valueOf = function sceValueOf() {
        return this.$$unwrapTrustedValue();
      };
      holderType.prototype.toString = function sceToString() {
        return this.$$unwrapTrustedValue().toString();
      };
      return holderType;
    }

    var trustedValueHolderBase = generateHolderType(),
        byType = {};

    byType[SCE_CONTEXTS.HTML] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.CSS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.URL] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.JS] = generateHolderType(trustedValueHolderBase);
    byType[SCE_CONTEXTS.RESOURCE_URL] = generateHolderType(byType[SCE_CONTEXTS.URL]);

    /**
     * @ngdoc method
     * @name $sceDelegate#trustAs
     *
     * @description
     * Returns a trusted representation of the parameter for the specified context. This trusted
     * object will later on be used as-is, without any security check, by bindings or directives
     * that require this security context.
     * For instance, marking a string as trusted for the `$sce.HTML` context will entirely bypass
     * the potential `$sanitize` call in corresponding `$sce.HTML` bindings or directives, such as
     * `ng-bind-html`. Note that in most cases you won't need to call this function: if you have the
     * sanitizer loaded, passing the value itself will render all the HTML that does not pose a
     * security risk.
     *
     * See {@link ng.$sceDelegate#getTrusted getTrusted} for the function that will consume those
     * trusted values, and {@link ng.$sce $sce} for general documentation about strict contextual
     * escaping.
     *
     * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
     *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
     *
     * @param {*} value The value that should be considered trusted.
     * @return {*} A trusted representation of value, that can be used in the given context.
     */
    function trustAs(type, trustedValue) {
      var Constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      if (!Constructor) {
        throw $sceMinErr('icontext',
            'Attempted to trust a value in invalid context. Context: {0}; Value: {1}',
            type, trustedValue);
      }
      if (trustedValue === null || isUndefined(trustedValue) || trustedValue === '') {
        return trustedValue;
      }
      // All the current contexts in SCE_CONTEXTS happen to be strings.  In order to avoid trusting
      // mutable objects, we ensure here that the value passed in is actually a string.
      if (typeof trustedValue !== 'string') {
        throw $sceMinErr('itype',
            'Attempted to trust a non-string value in a content requiring a string: Context: {0}',
            type);
      }
      return new Constructor(trustedValue);
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#valueOf
     *
     * @description
     * If the passed parameter had been returned by a prior call to {@link ng.$sceDelegate#trustAs
     * `$sceDelegate.trustAs`}, returns the value that had been passed to {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}.
     *
     * If the passed parameter is not a value that had been returned by {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}, it must be returned as-is.
     *
     * @param {*} value The result of a prior {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}
     *     call or anything else.
     * @return {*} The `value` that was originally provided to {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} if `value` is the result of such a call.  Otherwise, returns
     *     `value` unchanged.
     */
    function valueOf(maybeTrusted) {
      if (maybeTrusted instanceof trustedValueHolderBase) {
        return maybeTrusted.$$unwrapTrustedValue();
      } else {
        return maybeTrusted;
      }
    }

    /**
     * @ngdoc method
     * @name $sceDelegate#getTrusted
     *
     * @description
     * Takes any input, and either returns a value that's safe to use in the specified context, or
     * throws an exception.
     *
     * In practice, there are several cases. When given a string, this function runs checks
     * and sanitization to make it safe without prior assumptions. When given the result of a {@link
     * ng.$sceDelegate#trustAs `$sceDelegate.trustAs`} call, it returns the originally supplied
     * value if that value's context is valid for this call's context. Finally, this function can
     * also throw when there is no way to turn `maybeTrusted` in a safe value (e.g., no sanitization
     * is available or possible.)
     *
     * @param {string} type The context in which this value is to be used (such as `$sce.HTML`).
     * @param {*} maybeTrusted The result of a prior {@link ng.$sceDelegate#trustAs
     *     `$sceDelegate.trustAs`} call, or anything else (which will not be considered trusted.)
     * @return {*} A version of the value that's safe to use in the given context, or throws an
     *     exception if this is impossible.
     */
    function getTrusted(type, maybeTrusted) {
      if (maybeTrusted === null || isUndefined(maybeTrusted) || maybeTrusted === '') {
        return maybeTrusted;
      }
      var constructor = (byType.hasOwnProperty(type) ? byType[type] : null);
      // If maybeTrusted is a trusted class instance or subclass instance, then unwrap and return
      // as-is.
      if (constructor && maybeTrusted instanceof constructor) {
        return maybeTrusted.$$unwrapTrustedValue();
      }
      // Otherwise, if we get here, then we may either make it safe, or throw an exception. This
      // depends on the context: some are sanitizatible (HTML), some use whitelists (RESOURCE_URL),
      // some are impossible to do (JS). This step isn't implemented for CSS and URL, as AngularJS
      // has no corresponding sinks.
      if (type === SCE_CONTEXTS.RESOURCE_URL) {
        // RESOURCE_URL uses a whitelist.
        if (isResourceUrlAllowedByPolicy(maybeTrusted)) {
          return maybeTrusted;
        } else {
          throw $sceMinErr('insecurl',
              'Blocked loading resource from url not allowed by $sceDelegate policy.  URL: {0}',
              maybeTrusted.toString());
        }
      } else if (type === SCE_CONTEXTS.HTML) {
        // htmlSanitizer throws its own error when no sanitizer is available.
        return htmlSanitizer(maybeTrusted);
      }
      // Default error when the $sce service has no way to make the input safe.
      throw $sceMinErr('unsafe', 'Attempting to use an unsafe value in a safe context.');
    }

    return { trustAs: trustAs,
             getTrusted: getTrusted,
             valueOf: valueOf };
  }];
}


/**
 * @ngdoc provider
 * @name $sceProvider
 * @this
 *
 * @description
 *
 * The $sceProvider provider allows developers to configure the {@link ng.$sce $sce} service.
 * -   enable/disable Strict Contextual Escaping (SCE) in a module
 * -   override the default implementation with a custom delegate
 *
 * Read more about {@link ng.$sce Strict Contextual Escaping (SCE)}.
 */

/**
 * @ngdoc service
 * @name $sce
 * @kind function
 *
 * @description
 *
 * `$sce` is a service that provides Strict Contextual Escaping services to AngularJS.
 *
 * # Strict Contextual Escaping
 *
 * Strict Contextual Escaping (SCE) is a mode in which AngularJS constrains bindings to only render
 * trusted values. Its goal is to assist in writing code in a way that (a) is secure by default, and
 * (b) makes auditing for security vulnerabilities such as XSS, clickjacking, etc. a lot easier.
 *
 * ## Overview
 *
 * To systematically block XSS security bugs, AngularJS treats all values as untrusted by default in
 * HTML or sensitive URL bindings. When binding untrusted values, AngularJS will automatically
 * run security checks on them (sanitizations, whitelists, depending on context), or throw when it
 * cannot guarantee the security of the result. That behavior depends strongly on contexts: HTML
 * can be sanitized, but template URLs cannot, for instance.
 *
 * To illustrate this, consider the `ng-bind-html` directive. It renders its value directly as HTML:
 * we call that the *context*. When given an untrusted input, AngularJS will attempt to sanitize it
 * before rendering if a sanitizer is available, and throw otherwise. To bypass sanitization and
 * render the input as-is, you will need to mark it as trusted for that context before attempting
 * to bind it.
 *
 * As of version 1.2, AngularJS ships with SCE enabled by default.
 *
 * ## In practice
 *
 * Here's an example of a binding in a privileged context:
 *
 * ```
 * <input ng-model="userHtml" aria-label="User input">
 * <div ng-bind-html="userHtml"></div>
 * ```
 *
 * Notice that `ng-bind-html` is bound to `userHtml` controlled by the user.  With SCE
 * disabled, this application allows the user to render arbitrary HTML into the DIV, which would
 * be an XSS security bug. In a more realistic example, one may be rendering user comments, blog
 * articles, etc. via bindings. (HTML is just one example of a context where rendering user
 * controlled input creates security vulnerabilities.)
 *
 * For the case of HTML, you might use a library, either on the client side, or on the server side,
 * to sanitize unsafe HTML before binding to the value and rendering it in the document.
 *
 * How would you ensure that every place that used these types of bindings was bound to a value that
 * was sanitized by your library (or returned as safe for rendering by your server?)  How can you
 * ensure that you didn't accidentally delete the line that sanitized the value, or renamed some
 * properties/fields and forgot to update the binding to the sanitized value?
 *
 * To be secure by default, AngularJS makes sure bindings go through that sanitization, or
 * any similar validation process, unless there's a good reason to trust the given value in this
 * context.  That trust is formalized with a function call. This means that as a developer, you
 * can assume all untrusted bindings are safe. Then, to audit your code for binding security issues,
 * you just need to ensure the values you mark as trusted indeed are safe - because they were
 * received from your server, sanitized by your library, etc. You can organize your codebase to
 * help with this - perhaps allowing only the files in a specific directory to do this.
 * Ensuring that the internal API exposed by that code doesn't markup arbitrary values as safe then
 * becomes a more manageable task.
 *
 * In the case of AngularJS' SCE service, one uses {@link ng.$sce#trustAs $sce.trustAs}
 * (and shorthand methods such as {@link ng.$sce#trustAsHtml $sce.trustAsHtml}, etc.) to
 * build the trusted versions of your values.
 *
 * ## How does it work?
 *
 * In privileged contexts, directives and code will bind to the result of {@link ng.$sce#getTrusted
 * $sce.getTrusted(context, value)} rather than to the value directly.  Think of this function as
 * a way to enforce the required security context in your data sink. Directives use {@link
 * ng.$sce#parseAs $sce.parseAs} rather than `$parse` to watch attribute bindings, which performs
 * the {@link ng.$sce#getTrusted $sce.getTrusted} behind the scenes on non-constant literals. Also,
 * when binding without directives, AngularJS will understand the context of your bindings
 * automatically.
 *
 * As an example, {@link ng.directive:ngBindHtml ngBindHtml} uses {@link
 * ng.$sce#parseAsHtml $sce.parseAsHtml(binding expression)}.  Here's the actual code (slightly
 * simplified):
 *
 * ```
 * var ngBindHtmlDirective = ['$sce', function($sce) {
 *   return function(scope, element, attr) {
 *     scope.$watch($sce.parseAsHtml(attr.ngBindHtml), function(value) {
 *       element.html(value || '');
 *     });
 *   };
 * }];
 * ```
 *
 * ## Impact on loading templates
 *
 * This applies both to the {@link ng.directive:ngInclude `ng-include`} directive as well as
 * `templateUrl`'s specified by {@link guide/directive directives}.
 *
 * By default, Angular only loads templates from the same domain and protocol as the application
 * document.  This is done by calling {@link ng.$sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on the template URL.  To load templates from other domains and/or
 * protocols, you may either {@link ng.$sceDelegateProvider#resourceUrlWhitelist whitelist
 * them} or {@link ng.$sce#trustAsResourceUrl wrap it} into a trusted value.
 *
 * *Please note*:
 * The browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy apply in addition to this and may further restrict whether the template is successfully
 * loaded.  This means that without the right CORS policy, loading templates from a different domain
 * won't work on all browsers.  Also, loading templates from `file://` URL does not work on some
 * browsers.
 *
 * ## This feels like too much overhead
 *
 * It's important to remember that SCE only applies to interpolation expressions.
 *
 * If your expressions are constant literals, they're automatically trusted and you don't need to
 * call `$sce.trustAs` on them (e.g.
 * `<div ng-bind-html="'<b>implicitly trusted</b>'"></div>`) just works. The `$sceDelegate` will
 * also use the `$sanitize` service if it is available when binding untrusted values to
 * `$sce.HTML` context. AngularJS provides an implementation in `angular-sanitize.js`, and if you
 * wish to use it, you will also need to depend on the {@link ngSanitize `ngSanitize`} module in
 * your application.
 *
 * The included {@link ng.$sceDelegate $sceDelegate} comes with sane defaults to allow you to load
 * templates in `ng-include` from your application's domain without having to even know about SCE.
 * It blocks loading templates from other domains or loading templates over http from an https
 * served document.  You can change these by setting your own custom {@link
 * ng.$sceDelegateProvider#resourceUrlWhitelist whitelists} and {@link
 * ng.$sceDelegateProvider#resourceUrlBlacklist blacklists} for matching such URLs.
 *
 * This significantly reduces the overhead.  It is far easier to pay the small overhead and have an
 * application that's secure and can be audited to verify that with much more ease than bolting
 * security onto an application later.
 *
 * <a name="contexts"></a>
 * ## What trusted context types are supported?
 *
 * | Context             | Notes          |
 * |---------------------|----------------|
 * | `$sce.HTML`         | For HTML that's safe to source into the application.  The {@link ng.directive:ngBindHtml ngBindHtml} directive uses this context for bindings. If an unsafe value is encountered, and the {@link ngSanitize.$sanitize $sanitize} service is available (implemented by the {@link ngSanitize ngSanitize} module) this will sanitize the value instead of throwing an error. |
 * | `$sce.CSS`          | For CSS that's safe to source into the application.  Currently, no bindings require this context. Feel free to use it in your own directives. |
 * | `$sce.URL`          | For URLs that are safe to follow as links.  Currently unused (`<a href=`, `<img src=`, and some others sanitize their urls and don't constitute an SCE context.) |
 * | `$sce.RESOURCE_URL` | For URLs that are not only safe to follow as links, but whose contents are also safe to include in your application.  Examples include `ng-include`, `src` / `ngSrc` bindings for tags other than `IMG`, `VIDEO`, `AUDIO`, `SOURCE`, and `TRACK` (e.g. `IFRAME`, `OBJECT`, etc.)  <br><br>Note that `$sce.RESOURCE_URL` makes a stronger statement about the URL than `$sce.URL` does (it's not just the URL that matters, but also what is at the end of it), and therefore contexts requiring values trusted for `$sce.RESOURCE_URL` can be used anywhere that values trusted for `$sce.URL` are required. |
 * | `$sce.JS`           | For JavaScript that is safe to execute in your application's context.  Currently, no bindings require this context.  Feel free to use it in your own directives. |
 *
 *
 * Be aware that `a[href]` and `img[src]` automatically sanitize their URLs and do not pass them
 * through {@link ng.$sce#getTrusted $sce.getTrusted}. There's no CSS-, URL-, or JS-context bindings
 * in AngularJS currently, so their corresponding `$sce.trustAs` functions aren't useful yet. This
 * might evolve.
 *
 * ## Format of items in {@link ng.$sceDelegateProvider#resourceUrlWhitelist resourceUrlWhitelist}/{@link ng.$sceDelegateProvider#resourceUrlBlacklist Blacklist} <a name="resourceUrlPatternItem"></a>
 *
 *  Each element in these arrays must be one of the following:
 *
 *  - **'self'**
 *    - The special **string**, `'self'`, can be used to match against all URLs of the **same
 *      domain** as the application document using the **same protocol**.
 *  - **String** (except the special value `'self'`)
 *    - The string is matched against the full *normalized / absolute URL* of the resource
 *      being tested (substring matches are not good enough.)
 *    - There are exactly **two wildcard sequences** - `*` and `**`.  All other characters
 *      match themselves.
 *    - `*`: matches zero or more occurrences of any character other than one of the following 6
 *      characters: '`:`', '`/`', '`.`', '`?`', '`&`' and '`;`'.  It's a useful wildcard for use
 *      in a whitelist.
 *    - `**`: matches zero or more occurrences of *any* character.  As such, it's not
 *      appropriate for use in a scheme, domain, etc. as it would match too much.  (e.g.
 *      http://**.example.com/ would match http://evil.com/?ignore=.example.com/ and that might
 *      not have been the intention.)  Its usage at the very end of the path is ok.  (e.g.
 *      http://foo.example.com/templates/**).
 *  - **RegExp** (*see caveat below*)
 *    - *Caveat*:  While regular expressions are powerful and offer great flexibility,  their syntax
 *      (and all the inevitable escaping) makes them *harder to maintain*.  It's easy to
 *      accidentally introduce a bug when one updates a complex expression (imho, all regexes should
 *      have good test coverage).  For instance, the use of `.` in the regex is correct only in a
 *      small number of cases.  A `.` character in the regex used when matching the scheme or a
 *      subdomain could be matched against a `:` or literal `.` that was likely not intended.   It
 *      is highly recommended to use the string patterns and only fall back to regular expressions
 *      as a last resort.
 *    - The regular expression must be an instance of RegExp (i.e. not a string.)  It is
 *      matched against the **entire** *normalized / absolute URL* of the resource being tested
 *      (even when the RegExp did not have the `^` and `$` codes.)  In addition, any flags
 *      present on the RegExp (such as multiline, global, ignoreCase) are ignored.
 *    - If you are generating your JavaScript from some other templating engine (not
 *      recommended, e.g. in issue [#4006](https://github.com/angular/angular.js/issues/4006)),
 *      remember to escape your regular expression (and be aware that you might need more than
 *      one level of escaping depending on your templating engine and the way you interpolated
 *      the value.)  Do make use of your platform's escaping mechanism as it might be good
 *      enough before coding your own.  E.g. Ruby has
 *      [Regexp.escape(str)](http://www.ruby-doc.org/core-2.0.0/Regexp.html#method-c-escape)
 *      and Python has [re.escape](http://docs.python.org/library/re.html#re.escape).
 *      Javascript lacks a similar built in function for escaping.  Take a look at Google
 *      Closure library's [goog.string.regExpEscape(s)](
 *      http://docs.closure-library.googlecode.com/git/closure_goog_string_string.js.source.html#line962).
 *
 * Refer {@link ng.$sceDelegateProvider $sceDelegateProvider} for an example.
 *
 * ## Show me an example using SCE.
 *
 * <example module="mySceApp" deps="angular-sanitize.js" name="sce-service">
 * <file name="index.html">
 *   <div ng-controller="AppController as myCtrl">
 *     <i ng-bind-html="myCtrl.explicitlyTrustedHtml" id="explicitlyTrustedHtml"></i><br><br>
 *     <b>User comments</b><br>
 *     By default, HTML that isn't explicitly trusted (e.g. Alice's comment) is sanitized when
 *     $sanitize is available.  If $sanitize isn't available, this results in an error instead of an
 *     exploit.
 *     <div class="well">
 *       <div ng-repeat="userComment in myCtrl.userComments">
 *         <b>{{userComment.name}}</b>:
 *         <span ng-bind-html="userComment.htmlComment" class="htmlComment"></span>
 *         <br>
 *       </div>
 *     </div>
 *   </div>
 * </file>
 *
 * <file name="script.js">
 *   angular.module('mySceApp', ['ngSanitize'])
 *     .controller('AppController', ['$http', '$templateCache', '$sce',
 *       function AppController($http, $templateCache, $sce) {
 *         var self = this;
 *         $http.get('test_data.json', {cache: $templateCache}).then(function(response) {
 *           self.userComments = response.data;
 *         });
 *         self.explicitlyTrustedHtml = $sce.trustAsHtml(
 *             '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *             'sanitization.&quot;">Hover over this text.</span>');
 *       }]);
 * </file>
 *
 * <file name="test_data.json">
 * [
 *   { "name": "Alice",
 *     "htmlComment":
 *         "<span onmouseover='this.textContent=\"PWN3D!\"'>Is <i>anyone</i> reading this?</span>"
 *   },
 *   { "name": "Bob",
 *     "htmlComment": "<i>Yes!</i>  Am I the only other one?"
 *   }
 * ]
 * </file>
 *
 * <file name="protractor.js" type="protractor">
 *   describe('SCE doc demo', function() {
 *     it('should sanitize untrusted values', function() {
 *       expect(element.all(by.css('.htmlComment')).first().getAttribute('innerHTML'))
 *           .toBe('<span>Is <i>anyone</i> reading this?</span>');
 *     });
 *
 *     it('should NOT sanitize explicitly trusted values', function() {
 *       expect(element(by.id('explicitlyTrustedHtml')).getAttribute('innerHTML')).toBe(
 *           '<span onmouseover="this.textContent=&quot;Explicitly trusted HTML bypasses ' +
 *           'sanitization.&quot;">Hover over this text.</span>');
 *     });
 *   });
 * </file>
 * </example>
 *
 *
 *
 * ## Can I disable SCE completely?
 *
 * Yes, you can.  However, this is strongly discouraged.  SCE gives you a lot of security benefits
 * for little coding overhead.  It will be much harder to take an SCE disabled application and
 * either secure it on your own or enable SCE at a later stage.  It might make sense to disable SCE
 * for cases where you have a lot of existing code that was written before SCE was introduced and
 * you're migrating them a module at a time. Also do note that this is an app-wide setting, so if
 * you are writing a library, you will cause security bugs applications using it.
 *
 * That said, here's how you can completely disable SCE:
 *
 * ```
 * angular.module('myAppWithSceDisabledmyApp', []).config(function($sceProvider) {
 *   // Completely disable SCE.  For demonstration purposes only!
 *   // Do not use in new projects or libraries.
 *   $sceProvider.enabled(false);
 * });
 * ```
 *
 */

function $SceProvider() {
  var enabled = true;

  /**
   * @ngdoc method
   * @name $sceProvider#enabled
   * @kind function
   *
   * @param {boolean=} value If provided, then enables/disables SCE application-wide.
   * @return {boolean} True if SCE is enabled, false otherwise.
   *
   * @description
   * Enables/disables SCE and returns the current value.
   */
  this.enabled = function(value) {
    if (arguments.length) {
      enabled = !!value;
    }
    return enabled;
  };


  /* Design notes on the default implementation for SCE.
   *
   * The API contract for the SCE delegate
   * -------------------------------------
   * The SCE delegate object must provide the following 3 methods:
   *
   * - trustAs(contextEnum, value)
   *     This method is used to tell the SCE service that the provided value is OK to use in the
   *     contexts specified by contextEnum.  It must return an object that will be accepted by
   *     getTrusted() for a compatible contextEnum and return this value.
   *
   * - valueOf(value)
   *     For values that were not produced by trustAs(), return them as is.  For values that were
   *     produced by trustAs(), return the corresponding input value to trustAs.  Basically, if
   *     trustAs is wrapping the given values into some type, this operation unwraps it when given
   *     such a value.
   *
   * - getTrusted(contextEnum, value)
   *     This function should return the a value that is safe to use in the context specified by
   *     contextEnum or throw and exception otherwise.
   *
   * NOTE: This contract deliberately does NOT state that values returned by trustAs() must be
   * opaque or wrapped in some holder object.  That happens to be an implementation detail.  For
   * instance, an implementation could maintain a registry of all trusted objects by context.  In
   * such a case, trustAs() would return the same object that was passed in.  getTrusted() would
   * return the same object passed in if it was found in the registry under a compatible context or
   * throw an exception otherwise.  An implementation might only wrap values some of the time based
   * on some criteria.  getTrusted() might return a value and not throw an exception for special
   * constants or objects even if not wrapped.  All such implementations fulfill this contract.
   *
   *
   * A note on the inheritance model for SCE contexts
   * ------------------------------------------------
   * I've used inheritance and made RESOURCE_URL wrapped types a subtype of URL wrapped types.  This
   * is purely an implementation details.
   *
   * The contract is simply this:
   *
   *     getTrusted($sce.RESOURCE_URL, value) succeeding implies that getTrusted($sce.URL, value)
   *     will also succeed.
   *
   * Inheritance happens to capture this in a natural way. In some future, we may not use
   * inheritance anymore. That is OK because no code outside of sce.js and sceSpecs.js would need to
   * be aware of this detail.
   */

  this.$get = ['$parse', '$sceDelegate', function(
                $parse,   $sceDelegate) {
    // Support: IE 9-11 only
    // Prereq: Ensure that we're not running in IE<11 quirks mode.  In that mode, IE < 11 allow
    // the "expression(javascript expression)" syntax which is insecure.
    if (enabled && msie < 8) {
      throw $sceMinErr('iequirks',
        'Strict Contextual Escaping does not support Internet Explorer version < 11 in quirks ' +
        'mode.  You can fix this by adding the text <!doctype html> to the top of your HTML ' +
        'document.  See http://docs.angularjs.org/api/ng.$sce for more information.');
    }

    var sce = shallowCopy(SCE_CONTEXTS);

    /**
     * @ngdoc method
     * @name $sce#isEnabled
     * @kind function
     *
     * @return {Boolean} True if SCE is enabled, false otherwise.  If you want to set the value, you
     *     have to do it at module config time on {@link ng.$sceProvider $sceProvider}.
     *
     * @description
     * Returns a boolean indicating if SCE is enabled.
     */
    sce.isEnabled = function() {
      return enabled;
    };
    sce.trustAs = $sceDelegate.trustAs;
    sce.getTrusted = $sceDelegate.getTrusted;
    sce.valueOf = $sceDelegate.valueOf;

    if (!enabled) {
      sce.trustAs = sce.getTrusted = function(type, value) { return value; };
      sce.valueOf = identity;
    }

    /**
     * @ngdoc method
     * @name $sce#parseAs
     *
     * @description
     * Converts Angular {@link guide/expression expression} into a function.  This is like {@link
     * ng.$parse $parse} and is identical when the expression is a literal constant.  Otherwise, it
     * wraps the expression in a call to {@link ng.$sce#getTrusted $sce.getTrusted(*type*,
     * *result*)}
     *
     * @param {string} type The SCE context in which this result will be used.
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */
    sce.parseAs = function sceParseAs(type, expr) {
      var parsed = $parse(expr);
      if (parsed.literal && parsed.constant) {
        return parsed;
      } else {
        return $parse(expr, function(value) {
          return sce.getTrusted(type, value);
        });
      }
    };

    /**
     * @ngdoc method
     * @name $sce#trustAs
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs`}. As such, returns a
     * wrapped object that represents your value, and the trust you have in its safety for the given
     * context. AngularJS can then use that value as-is in bindings of the specified secure context.
     * This is used in bindings for `ng-bind-html`, `ng-include`, and most `src` attribute
     * interpolations. See {@link ng.$sce $sce} for strict contextual escaping.
     *
     * @param {string} type The context in which this value is safe for use, e.g. `$sce.URL`,
     *     `$sce.RESOURCE_URL`, `$sce.HTML`, `$sce.JS` or `$sce.CSS`.
     *
     * @param {*} value The value that that should be considered trusted.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in the context you specified.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsHtml
     *
     * @description
     * Shorthand method.  `$sce.trustAsHtml(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.HTML, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.HTML` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.HTML` context (like `ng-bind-html`).
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsCss
     *
     * @description
     * Shorthand method.  `$sce.trustAsCss(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.CSS, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.CSS` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant
     *     of your `value` in `$sce.CSS` context. This context is currently unused, so there are
     *     almost no reasons to use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.URL, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.URL` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.URL` context. That context is currently unused, so there are almost no reasons
     *     to use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.trustAsResourceUrl(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.RESOURCE_URL` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.RESOURCE_URL` context (template URLs in `ng-include`, most `src` attribute
     *     bindings, ...)
     */

    /**
     * @ngdoc method
     * @name $sce#trustAsJs
     *
     * @description
     * Shorthand method.  `$sce.trustAsJs(value)` →
     *     {@link ng.$sceDelegate#trustAs `$sceDelegate.trustAs($sce.JS, value)`}
     *
     * @param {*} value The value to mark as trusted for `$sce.JS` context.
     * @return {*} A wrapped version of value that can be used as a trusted variant of your `value`
     *     in `$sce.JS` context. That context is currently unused, so there are almost no reasons to
     *     use this function so far.
     */

    /**
     * @ngdoc method
     * @name $sce#getTrusted
     *
     * @description
     * Delegates to {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted`}.  As such,
     * takes any input, and either returns a value that's safe to use in the specified context,
     * or throws an exception. This function is aware of trusted values created by the `trustAs`
     * function and its shorthands, and when contexts are appropriate, returns the unwrapped value
     * as-is. Finally, this function can also throw when there is no way to turn `maybeTrusted` in a
     * safe value (e.g., no sanitization is available or possible.)
     *
     * @param {string} type The context in which this value is to be used.
     * @param {*} maybeTrusted The result of a prior {@link ng.$sce#trustAs
     *     `$sce.trustAs`} call, or anything else (which will not be considered trusted.)
     * @return {*} A version of the value that's safe to use in the given context, or throws an
     *     exception if this is impossible.
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedHtml
     *
     * @description
     * Shorthand method.  `$sce.getTrustedHtml(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.HTML, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.HTML, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedCss
     *
     * @description
     * Shorthand method.  `$sce.getTrustedCss(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.CSS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.CSS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.URL, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.getTrustedResourceUrl(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.RESOURCE_URL, value)`}
     *
     * @param {*} value The value to pass to `$sceDelegate.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.RESOURCE_URL, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#getTrustedJs
     *
     * @description
     * Shorthand method.  `$sce.getTrustedJs(value)` →
     *     {@link ng.$sceDelegate#getTrusted `$sceDelegate.getTrusted($sce.JS, value)`}
     *
     * @param {*} value The value to pass to `$sce.getTrusted`.
     * @return {*} The return value of `$sce.getTrusted($sce.JS, value)`
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsHtml
     *
     * @description
     * Shorthand method.  `$sce.parseAsHtml(expression string)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.HTML, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsCss
     *
     * @description
     * Shorthand method.  `$sce.parseAsCss(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.CSS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsResourceUrl
     *
     * @description
     * Shorthand method.  `$sce.parseAsResourceUrl(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.RESOURCE_URL, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    /**
     * @ngdoc method
     * @name $sce#parseAsJs
     *
     * @description
     * Shorthand method.  `$sce.parseAsJs(value)` →
     *     {@link ng.$sce#parseAs `$sce.parseAs($sce.JS, value)`}
     *
     * @param {string} expression String expression to compile.
     * @return {function(context, locals)} A function which represents the compiled expression:
     *
     *    * `context` – `{object}` – an object against which any expressions embedded in the
     *      strings are evaluated against (typically a scope object).
     *    * `locals` – `{object=}` – local variables context object, useful for overriding values
     *      in `context`.
     */

    // Shorthand delegations.
    var parse = sce.parseAs,
        getTrusted = sce.getTrusted,
        trustAs = sce.trustAs;

    forEach(SCE_CONTEXTS, function(enumValue, name) {
      var lName = lowercase(name);
      sce[snakeToCamel('parse_as_' + lName)] = function(expr) {
        return parse(enumValue, expr);
      };
      sce[snakeToCamel('get_trusted_' + lName)] = function(value) {
        return getTrusted(enumValue, value);
      };
      sce[snakeToCamel('trust_as_' + lName)] = function(value) {
        return trustAs(enumValue, value);
      };
    });

    return sce;
  }];
}

/* exported $SnifferProvider */

/**
 * !!! This is an undocumented "private" service !!!
 *
 * @name $sniffer
 * @requires $window
 * @requires $document
 * @this
 *
 * @property {boolean} history Does the browser support html5 history api ?
 * @property {boolean} transitions Does the browser support CSS transition events ?
 * @property {boolean} animations Does the browser support CSS animation events ?
 *
 * @description
 * This is very simple implementation of testing browser's features.
 */
function $SnifferProvider() {
  this.$get = ['$window', '$document', function($window, $document) {
    var eventSupport = {},
        // Chrome Packaged Apps are not allowed to access `history.pushState`.
        // If not sandboxed, they can be detected by the presence of `chrome.app.runtime`
        // (see https://developer.chrome.com/apps/api_index). If sandboxed, they can be detected by
        // the presence of an extension runtime ID and the absence of other Chrome runtime APIs
        // (see https://developer.chrome.com/apps/manifest/sandbox).
        // (NW.js apps have access to Chrome APIs, but do support `history`.)
        isNw = $window.nw && $window.nw.process,
        isChromePackagedApp =
            !isNw &&
            $window.chrome &&
            ($window.chrome.app && $window.chrome.app.runtime ||
                !$window.chrome.app && $window.chrome.runtime && $window.chrome.runtime.id),
        hasHistoryPushState = !isChromePackagedApp && $window.history && $window.history.pushState,
        android =
          toInt((/android (\d+)/.exec(lowercase(($window.navigator || {}).userAgent)) || [])[1]),
        boxee = /Boxee/i.test(($window.navigator || {}).userAgent),
        document = $document[0] || {},
        bodyStyle = document.body && document.body.style,
        transitions = false,
        animations = false;

    if (bodyStyle) {
      // Support: Android <5, Blackberry Browser 10, default Chrome in Android 4.4.x
      // Mentioned browsers need a -webkit- prefix for transitions & animations.
      transitions = !!('transition' in bodyStyle || 'webkitTransition' in bodyStyle);
      animations = !!('animation' in bodyStyle || 'webkitAnimation' in bodyStyle);
    }


    return {
      // Android has history.pushState, but it does not update location correctly
      // so let's not use the history API at all.
      // http://code.google.com/p/android/issues/detail?id=17471
      // https://github.com/angular/angular.js/issues/904

      // older webkit browser (533.9) on Boxee box has exactly the same problem as Android has
      // so let's not use the history API also
      // We are purposefully using `!(android < 4)` to cover the case when `android` is undefined
      history: !!(hasHistoryPushState && !(android < 4) && !boxee),
      hasEvent: function(event) {
        // Support: IE 9-11 only
        // IE9 implements 'input' event it's so fubared that we rather pretend that it doesn't have
        // it. In particular the event is not fired when backspace or delete key are pressed or
        // when cut operation is performed.
        // IE10+ implements 'input' event but it erroneously fires under various situations,
        // e.g. when placeholder changes, or a form is focused.
        if (event === 'input' && msie) return false;

        if (isUndefined(eventSupport[event])) {
          var divElm = document.createElement('div');
          eventSupport[event] = 'on' + event in divElm;
        }

        return eventSupport[event];
      },
      csp: csp(),
      transitions: transitions,
      animations: animations,
      android: android
    };
  }];
}

var $templateRequestMinErr = minErr('$compile');

/**
 * @ngdoc provider
 * @name $templateRequestProvider
 * @this
 *
 * @description
 * Used to configure the options passed to the {@link $http} service when making a template request.
 *
 * For example, it can be used for specifying the "Accept" header that is sent to the server, when
 * requesting a template.
 */
function $TemplateRequestProvider() {

  var httpOptions;

  /**
   * @ngdoc method
   * @name $templateRequestProvider#httpOptions
   * @description
   * The options to be passed to the {@link $http} service when making the request.
   * You can use this to override options such as the "Accept" header for template requests.
   *
   * The {@link $templateRequest} will set the `cache` and the `transformResponse` properties of the
   * options if not overridden here.
   *
   * @param {string=} value new value for the {@link $http} options.
   * @returns {string|self} Returns the {@link $http} options when used as getter and self if used as setter.
   */
  this.httpOptions = function(val) {
    if (val) {
      httpOptions = val;
      return this;
    }
    return httpOptions;
  };

  /**
   * @ngdoc service
   * @name $templateRequest
   *
   * @description
   * The `$templateRequest` service runs security checks then downloads the provided template using
   * `$http` and, upon success, stores the contents inside of `$templateCache`. If the HTTP request
   * fails or the response data of the HTTP request is empty, a `$compile` error will be thrown (the
   * exception can be thwarted by setting the 2nd parameter of the function to true). Note that the
   * contents of `$templateCache` are trusted, so the call to `$sce.getTrustedUrl(tpl)` is omitted
   * when `tpl` is of type string and `$templateCache` has the matching entry.
   *
   * If you want to pass custom options to the `$http` service, such as setting the Accept header you
   * can configure this via {@link $templateRequestProvider#httpOptions}.
   *
   * @param {string|TrustedResourceUrl} tpl The HTTP request template URL
   * @param {boolean=} ignoreRequestError Whether or not to ignore the exception when the request fails or the template is empty
   *
   * @return {Promise} a promise for the HTTP response data of the given URL.
   *
   * @property {number} totalPendingRequests total amount of pending template requests being downloaded.
   */
  this.$get = ['$exceptionHandler', '$templateCache', '$http', '$q', '$sce',
    function($exceptionHandler, $templateCache, $http, $q, $sce) {

      function handleRequestFn(tpl, ignoreRequestError) {
        handleRequestFn.totalPendingRequests++;

        // We consider the template cache holds only trusted templates, so
        // there's no need to go through whitelisting again for keys that already
        // are included in there. This also makes Angular accept any script
        // directive, no matter its name. However, we still need to unwrap trusted
        // types.
        if (!isString(tpl) || isUndefined($templateCache.get(tpl))) {
          tpl = $sce.getTrustedResourceUrl(tpl);
        }

        var transformResponse = $http.defaults && $http.defaults.transformResponse;

        if (isArray(transformResponse)) {
          transformResponse = transformResponse.filter(function(transformer) {
            return transformer !== defaultHttpResponseTransform;
          });
        } else if (transformResponse === defaultHttpResponseTransform) {
          transformResponse = null;
        }

        return $http.get(tpl, extend({
            cache: $templateCache,
            transformResponse: transformResponse
          }, httpOptions))
          .finally(function() {
            handleRequestFn.totalPendingRequests--;
          })
          .then(function(response) {
            $templateCache.put(tpl, response.data);
            return response.data;
          }, handleError);

        function handleError(resp) {
          if (!ignoreRequestError) {
            resp = $templateRequestMinErr('tpload',
                'Failed to load template: {0} (HTTP status: {1} {2})',
                tpl, resp.status, resp.statusText);

            $exceptionHandler(resp);
          }

          return $q.reject(resp);
        }
      }

      handleRequestFn.totalPendingRequests = 0;

      return handleRequestFn;
    }
  ];
}

/** @this */
function $$TestabilityProvider() {
  this.$get = ['$rootScope', '$browser', '$location',
       function($rootScope,   $browser,   $location) {

    /**
     * @name $testability
     *
     * @description
     * The private $$testability service provides a collection of methods for use when debugging
     * or by automated test and debugging tools.
     */
    var testability = {};

    /**
     * @name $$testability#findBindings
     *
     * @description
     * Returns an array of elements that are bound (via ng-bind or {{}})
     * to expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The binding expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression. Filters and whitespace are ignored.
     */
    testability.findBindings = function(element, expression, opt_exactMatch) {
      var bindings = element.getElementsByClassName('ng-binding');
      var matches = [];
      forEach(bindings, function(binding) {
        var dataBinding = angular.element(binding).data('$binding');
        if (dataBinding) {
          forEach(dataBinding, function(bindingName) {
            if (opt_exactMatch) {
              var matcher = new RegExp('(^|\\s)' + escapeForRegexp(expression) + '(\\s|\\||$)');
              if (matcher.test(bindingName)) {
                matches.push(binding);
              }
            } else {
              if (bindingName.indexOf(expression) !== -1) {
                matches.push(binding);
              }
            }
          });
        }
      });
      return matches;
    };

    /**
     * @name $$testability#findModels
     *
     * @description
     * Returns an array of elements that are two-way found via ng-model to
     * expressions matching the input.
     *
     * @param {Element} element The element root to search from.
     * @param {string} expression The model expression to match.
     * @param {boolean} opt_exactMatch If true, only returns exact matches
     *     for the expression.
     */
    testability.findModels = function(element, expression, opt_exactMatch) {
      var prefixes = ['ng-', 'data-ng-', 'ng\\:'];
      for (var p = 0; p < prefixes.length; ++p) {
        var attributeEquals = opt_exactMatch ? '=' : '*=';
        var selector = '[' + prefixes[p] + 'model' + attributeEquals + '"' + expression + '"]';
        var elements = element.querySelectorAll(selector);
        if (elements.length) {
          return elements;
        }
      }
    };

    /**
     * @name $$testability#getLocation
     *
     * @description
     * Shortcut for getting the location in a browser agnostic way. Returns
     *     the path, search, and hash. (e.g. /path?a=b#hash)
     */
    testability.getLocation = function() {
      return $location.url();
    };

    /**
     * @name $$testability#setLocation
     *
     * @description
     * Shortcut for navigating to a location without doing a full page reload.
     *
     * @param {string} url The location url (path, search and hash,
     *     e.g. /path?a=b#hash) to go to.
     */
    testability.setLocation = function(url) {
      if (url !== $location.url()) {
        $location.url(url);
        $rootScope.$digest();
      }
    };

    /**
     * @name $$testability#whenStable
     *
     * @description
     * Calls the callback when $timeout and $http requests are completed.
     *
     * @param {function} callback
     */
    testability.whenStable = function(callback) {
      $browser.notifyWhenNoOutstandingRequests(callback);
    };

    return testability;
  }];
}

/** @this */
function $TimeoutProvider() {
  this.$get = ['$rootScope', '$browser', '$q', '$$q', '$exceptionHandler',
       function($rootScope,   $browser,   $q,   $$q,   $exceptionHandler) {

    var deferreds = {};


     /**
      * @ngdoc service
      * @name $timeout
      *
      * @description
      * Angular's wrapper for `window.setTimeout`. The `fn` function is wrapped into a try/catch
      * block and delegates any exceptions to
      * {@link ng.$exceptionHandler $exceptionHandler} service.
      *
      * The return value of calling `$timeout` is a promise, which will be resolved when
      * the delay has passed and the timeout function, if provided, is executed.
      *
      * To cancel a timeout request, call `$timeout.cancel(promise)`.
      *
      * In tests you can use {@link ngMock.$timeout `$timeout.flush()`} to
      * synchronously flush the queue of deferred functions.
      *
      * If you only want a promise that will be resolved after some specified delay
      * then you can call `$timeout` without the `fn` function.
      *
      * @param {function()=} fn A function, whose execution should be delayed.
      * @param {number=} [delay=0] Delay in milliseconds.
      * @param {boolean=} [invokeApply=true] If set to `false` skips model dirty checking, otherwise
      *   will invoke `fn` within the {@link ng.$rootScope.Scope#$apply $apply} block.
      * @param {...*=} Pass additional parameters to the executed function.
      * @returns {Promise} Promise that will be resolved when the timeout is reached. The promise
      *   will be resolved with the return value of the `fn` function.
      *
      */
    function timeout(fn, delay, invokeApply) {
      if (!isFunction(fn)) {
        invokeApply = delay;
        delay = fn;
        fn = noop;
      }

      var args = sliceArgs(arguments, 3),
          skipApply = (isDefined(invokeApply) && !invokeApply),
          deferred = (skipApply ? $$q : $q).defer(),
          promise = deferred.promise,
          timeoutId;

      timeoutId = $browser.defer(function() {
        try {
          deferred.resolve(fn.apply(null, args));
        } catch (e) {
          deferred.reject(e);
          $exceptionHandler(e);
        } finally {
          delete deferreds[promise.$$timeoutId];
        }

        if (!skipApply) $rootScope.$apply();
      }, delay);

      promise.$$timeoutId = timeoutId;
      deferreds[timeoutId] = deferred;

      return promise;
    }


     /**
      * @ngdoc method
      * @name $timeout#cancel
      *
      * @description
      * Cancels a task associated with the `promise`. As a result of this, the promise will be
      * resolved with a rejection.
      *
      * @param {Promise=} promise Promise returned by the `$timeout` function.
      * @returns {boolean} Returns `true` if the task hasn't executed yet and was successfully
      *   canceled.
      */
    timeout.cancel = function(promise) {
      if (promise && promise.$$timeoutId in deferreds) {
        // Timeout cancels should not report an unhandled promise.
        markQExceptionHandled(deferreds[promise.$$timeoutId].promise);
        deferreds[promise.$$timeoutId].reject('canceled');
        delete deferreds[promise.$$timeoutId];
        return $browser.defer.cancel(promise.$$timeoutId);
      }
      return false;
    };

    return timeout;
  }];
}

// NOTE:  The usage of window and document instead of $window and $document here is
// deliberate.  This service depends on the specific behavior of anchor nodes created by the
// browser (resolving and parsing URLs) that is unlikely to be provided by mock objects and
// cause us to break tests.  In addition, when the browser resolves a URL for XHR, it
// doesn't know about mocked locations and resolves URLs to the real document - which is
// exactly the behavior needed here.  There is little value is mocking these out for this
// service.
var urlParsingNode = window.document.createElement('a');
var originUrl = urlResolve(window.location.href);


/**
 *
 * Implementation Notes for non-IE browsers
 * ----------------------------------------
 * Assigning a URL to the href property of an anchor DOM node, even one attached to the DOM,
 * results both in the normalizing and parsing of the URL.  Normalizing means that a relative
 * URL will be resolved into an absolute URL in the context of the application document.
 * Parsing means that the anchor node's host, hostname, protocol, port, pathname and related
 * properties are all populated to reflect the normalized URL.  This approach has wide
 * compatibility - Safari 1+, Mozilla 1+ etc.  See
 * http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *
 * Implementation Notes for IE
 * ---------------------------
 * IE <= 10 normalizes the URL when assigned to the anchor node similar to the other
 * browsers.  However, the parsed components will not be set if the URL assigned did not specify
 * them.  (e.g. if you assign a.href = "foo", then a.protocol, a.host, etc. will be empty.)  We
 * work around that by performing the parsing in a 2nd step by taking a previously normalized
 * URL (e.g. by assigning to a.href) and assigning it a.href again.  This correctly populates the
 * properties such as protocol, hostname, port, etc.
 *
 * References:
 *   http://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement
 *   http://www.aptana.com/reference/html/api/HTMLAnchorElement.html
 *   http://url.spec.whatwg.org/#urlutils
 *   https://github.com/angular/angular.js/pull/2902
 *   http://james.padolsey.com/javascript/parsing-urls-with-the-dom/
 *
 * @kind function
 * @param {string} url The URL to be parsed.
 * @description Normalizes and parses a URL.
 * @returns {object} Returns the normalized URL as a dictionary.
 *
 *   | member name   | Description    |
 *   |---------------|----------------|
 *   | href          | A normalized version of the provided URL if it was not an absolute URL |
 *   | protocol      | The protocol including the trailing colon                              |
 *   | host          | The host and port (if the port is non-default) of the normalizedUrl    |
 *   | search        | The search params, minus the question mark                             |
 *   | hash          | The hash string, minus the hash symbol
 *   | hostname      | The hostname
 *   | port          | The port, without ":"
 *   | pathname      | The pathname, beginning with "/"
 *
 */
function urlResolve(url) {
  var href = url;

  // Support: IE 9-11 only
  if (msie) {
    // Normalize before parse.  Refer Implementation Notes on why this is
    // done in two steps on IE.
    urlParsingNode.setAttribute('href', href);
    href = urlParsingNode.href;
  }

  urlParsingNode.setAttribute('href', href);

  // urlParsingNode provides the UrlUtils interface - http://url.spec.whatwg.org/#urlutils
  return {
    href: urlParsingNode.href,
    protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
    host: urlParsingNode.host,
    search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
    hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
    hostname: urlParsingNode.hostname,
    port: urlParsingNode.port,
    pathname: (urlParsingNode.pathname.charAt(0) === '/')
      ? urlParsingNode.pathname
      : '/' + urlParsingNode.pathname
  };
}

/**
 * Parse a request URL and determine whether this is a same-origin request as the application document.
 *
 * @param {string|object} requestUrl The url of the request as a string that will be resolved
 * or a parsed URL object.
 * @returns {boolean} Whether the request is for the same origin as the application document.
 */
function urlIsSameOrigin(requestUrl) {
  var parsed = (isString(requestUrl)) ? urlResolve(requestUrl) : requestUrl;
  return (parsed.protocol === originUrl.protocol &&
          parsed.host === originUrl.host);
}

/**
 * @ngdoc service
 * @name $window
 * @this
 *
 * @description
 * A reference to the browser's `window` object. While `window`
 * is globally available in JavaScript, it causes testability problems, because
 * it is a global variable. In angular we always refer to it through the
 * `$window` service, so it may be overridden, removed or mocked for testing.
 *
 * Expressions, like the one defined for the `ngClick` directive in the example
 * below, are evaluated with respect to the current scope.  Therefore, there is
 * no risk of inadvertently coding in a dependency on a global value in such an
 * expression.
 *
 * @example
   <example module="windowExample" name="window-service">
     <file name="index.html">
       <script>
         angular.module('windowExample', [])
           .controller('ExampleController', ['$scope', '$window', function($scope, $window) {
             $scope.greeting = 'Hello, World!';
             $scope.doGreeting = function(greeting) {
               $window.alert(greeting);
             };
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="text" ng-model="greeting" aria-label="greeting" />
         <button ng-click="doGreeting(greeting)">ALERT</button>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
      it('should display the greeting in the input box', function() {
       element(by.model('greeting')).sendKeys('Hello, E2E Tests');
       // If we click the button it will block the test runner
       // element(':button').click();
      });
     </file>
   </example>
 */
function $WindowProvider() {
  this.$get = valueFn(window);
}

/**
 * @name $$cookieReader
 * @requires $document
 *
 * @description
 * This is a private service for reading cookies used by $http and ngCookies
 *
 * @return {Object} a key/value map of the current cookies
 */
function $$CookieReader($document) {
  var rawDocument = $document[0] || {};
  var lastCookies = {};
  var lastCookieString = '';

  function safeGetCookie(rawDocument) {
    try {
      return rawDocument.cookie || '';
    } catch (e) {
      return '';
    }
  }

  function safeDecodeURIComponent(str) {
    try {
      return decodeURIComponent(str);
    } catch (e) {
      return str;
    }
  }

  return function() {
    var cookieArray, cookie, i, index, name;
    var currentCookieString = safeGetCookie(rawDocument);

    if (currentCookieString !== lastCookieString) {
      lastCookieString = currentCookieString;
      cookieArray = lastCookieString.split('; ');
      lastCookies = {};

      for (i = 0; i < cookieArray.length; i++) {
        cookie = cookieArray[i];
        index = cookie.indexOf('=');
        if (index > 0) { //ignore nameless cookies
          name = safeDecodeURIComponent(cookie.substring(0, index));
          // the first value that is seen for a cookie is the most
          // specific one.  values for the same cookie name that
          // follow are for less specific paths.
          if (isUndefined(lastCookies[name])) {
            lastCookies[name] = safeDecodeURIComponent(cookie.substring(index + 1));
          }
        }
      }
    }
    return lastCookies;
  };
}

$$CookieReader.$inject = ['$document'];

/** @this */
function $$CookieReaderProvider() {
  this.$get = $$CookieReader;
}

/* global currencyFilter: true,
 dateFilter: true,
 filterFilter: true,
 jsonFilter: true,
 limitToFilter: true,
 lowercaseFilter: true,
 numberFilter: true,
 orderByFilter: true,
 uppercaseFilter: true,
 */

/**
 * @ngdoc provider
 * @name $filterProvider
 * @description
 *
 * Filters are just functions which transform input to an output. However filters need to be
 * Dependency Injected. To achieve this a filter definition consists of a factory function which is
 * annotated with dependencies and is responsible for creating a filter function.
 *
 * <div class="alert alert-warning">
 * **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
 * Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
 * your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
 * (`myapp_subsection_filterx`).
 * </div>
 *
 * ```js
 *   // Filter registration
 *   function MyModule($provide, $filterProvider) {
 *     // create a service to demonstrate injection (not always needed)
 *     $provide.value('greet', function(name){
 *       return 'Hello ' + name + '!';
 *     });
 *
 *     // register a filter factory which uses the
 *     // greet service to demonstrate DI.
 *     $filterProvider.register('greet', function(greet){
 *       // return the filter function which uses the greet service
 *       // to generate salutation
 *       return function(text) {
 *         // filters need to be forgiving so check input validity
 *         return text && greet(text) || text;
 *       };
 *     });
 *   }
 * ```
 *
 * The filter function is registered with the `$injector` under the filter name suffix with
 * `Filter`.
 *
 * ```js
 *   it('should be the same instance', inject(
 *     function($filterProvider) {
 *       $filterProvider.register('reverse', function(){
 *         return ...;
 *       });
 *     },
 *     function($filter, reverseFilter) {
 *       expect($filter('reverse')).toBe(reverseFilter);
 *     });
 * ```
 *
 *
 * For more information about how angular filters work, and how to create your own filters, see
 * {@link guide/filter Filters} in the Angular Developer Guide.
 */

/**
 * @ngdoc service
 * @name $filter
 * @kind function
 * @description
 * Filters are used for formatting data displayed to the user.
 *
 * They can be used in view templates, controllers or services.Angular comes
 * with a collection of [built-in filters](api/ng/filter), but it is easy to
 * define your own as well.
 *
 * The general syntax in templates is as follows:
 *
 * ```html
 * {{ expression [| filter_name[:parameter_value] ... ] }}
 * ```
 *
 * @param {String} name Name of the filter function to retrieve
 * @return {Function} the filter function
 * @example
   <example name="$filter" module="filterExample">
     <file name="index.html">
       <div ng-controller="MainCtrl">
        <h3>{{ originalText }}</h3>
        <h3>{{ filteredText }}</h3>
       </div>
     </file>

     <file name="script.js">
      angular.module('filterExample', [])
      .controller('MainCtrl', function($scope, $filter) {
        $scope.originalText = 'hello';
        $scope.filteredText = $filter('uppercase')($scope.originalText);
      });
     </file>
   </example>
  */
$FilterProvider.$inject = ['$provide'];
/** @this */
function $FilterProvider($provide) {
  var suffix = 'Filter';

  /**
   * @ngdoc method
   * @name $filterProvider#register
   * @param {string|Object} name Name of the filter function, or an object map of filters where
   *    the keys are the filter names and the values are the filter factories.
   *
   *    <div class="alert alert-warning">
   *    **Note:** Filter names must be valid angular {@link expression} identifiers, such as `uppercase` or `orderBy`.
   *    Names with special characters, such as hyphens and dots, are not allowed. If you wish to namespace
   *    your filters, then you can use capitalization (`myappSubsectionFilterx`) or underscores
   *    (`myapp_subsection_filterx`).
   *    </div>
    * @param {Function} factory If the first argument was a string, a factory function for the filter to be registered.
   * @returns {Object} Registered filter instance, or if a map of filters was provided then a map
   *    of the registered filter instances.
   */
  function register(name, factory) {
    if (isObject(name)) {
      var filters = {};
      forEach(name, function(filter, key) {
        filters[key] = register(key, filter);
      });
      return filters;
    } else {
      return $provide.factory(name + suffix, factory);
    }
  }
  this.register = register;

  this.$get = ['$injector', function($injector) {
    return function(name) {
      return $injector.get(name + suffix);
    };
  }];

  ////////////////////////////////////////

  /* global
    currencyFilter: false,
    dateFilter: false,
    filterFilter: false,
    jsonFilter: false,
    limitToFilter: false,
    lowercaseFilter: false,
    numberFilter: false,
    orderByFilter: false,
    uppercaseFilter: false
  */

  register('currency', currencyFilter);
  register('date', dateFilter);
  register('filter', filterFilter);
  register('json', jsonFilter);
  register('limitTo', limitToFilter);
  register('lowercase', lowercaseFilter);
  register('number', numberFilter);
  register('orderBy', orderByFilter);
  register('uppercase', uppercaseFilter);
}

/**
 * @ngdoc filter
 * @name filter
 * @kind function
 *
 * @description
 * Selects a subset of items from `array` and returns it as a new array.
 *
 * @param {Array} array The source array.
 * <div class="alert alert-info">
 *   **Note**: If the array contains objects that reference themselves, filtering is not possible.
 * </div>
 * @param {string|Object|function()} expression The predicate to be used for selecting items from
 *   `array`.
 *
 *   Can be one of:
 *
 *   - `string`: The string is used for matching against the contents of the `array`. All strings or
 *     objects with string properties in `array` that match this string will be returned. This also
 *     applies to nested object properties.
 *     The predicate can be negated by prefixing the string with `!`.
 *
 *   - `Object`: A pattern object can be used to filter specific properties on objects contained
 *     by `array`. For example `{name:"M", phone:"1"}` predicate will return an array of items
 *     which have property `name` containing "M" and property `phone` containing "1". A special
 *     property name (`$` by default) can be used (e.g. as in `{$: "text"}`) to accept a match
 *     against any property of the object or its nested object properties. That's equivalent to the
 *     simple substring match with a `string` as described above. The special property name can be
 *     overwritten, using the `anyPropertyKey` parameter.
 *     The predicate can be negated by prefixing the string with `!`.
 *     For example `{name: "!M"}` predicate will return an array of items which have property `name`
 *     not containing "M".
 *
 *     Note that a named property will match properties on the same level only, while the special
 *     `$` property will match properties on the same level or deeper. E.g. an array item like
 *     `{name: {first: 'John', last: 'Doe'}}` will **not** be matched by `{name: 'John'}`, but
 *     **will** be matched by `{$: 'John'}`.
 *
 *   - `function(value, index, array)`: A predicate function can be used to write arbitrary filters.
 *     The function is called for each element of the array, with the element, its index, and
 *     the entire array itself as arguments.
 *
 *     The final result is an array of those elements that the predicate returned true for.
 *
 * @param {function(actual, expected)|true|false} [comparator] Comparator which is used in
 *     determining if values retrieved using `expression` (when it is not a function) should be
 *     considered a match based on the expected value (from the filter expression) and actual
 *     value (from the object in the array).
 *
 *   Can be one of:
 *
 *   - `function(actual, expected)`:
 *     The function will be given the object value and the predicate value to compare and
 *     should return true if both values should be considered equal.
 *
 *   - `true`: A shorthand for `function(actual, expected) { return angular.equals(actual, expected)}`.
 *     This is essentially strict comparison of expected and actual.
 *
 *   - `false`: A short hand for a function which will look for a substring match in a case
 *     insensitive way. Primitive values are converted to strings. Objects are not compared against
 *     primitives, unless they have a custom `toString` method (e.g. `Date` objects).
 *
 *
 *   Defaults to `false`.
 *
 * @param {string} [anyPropertyKey] The special property name that matches against any property.
 *     By default `$`.
 *
 * @example
   <example name="filter-filter">
     <file name="index.html">
       <div ng-init="friends = [{name:'John', phone:'555-1276'},
                                {name:'Mary', phone:'800-BIG-MARY'},
                                {name:'Mike', phone:'555-4321'},
                                {name:'Adam', phone:'555-5678'},
                                {name:'Julie', phone:'555-8765'},
                                {name:'Juliette', phone:'555-5678'}]"></div>

       <label>Search: <input ng-model="searchText"></label>
       <table id="searchTextResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friend in friends | filter:searchText">
           <td>{{friend.name}}</td>
           <td>{{friend.phone}}</td>
         </tr>
       </table>
       <hr>
       <label>Any: <input ng-model="search.$"></label> <br>
       <label>Name only <input ng-model="search.name"></label><br>
       <label>Phone only <input ng-model="search.phone"></label><br>
       <label>Equality <input type="checkbox" ng-model="strict"></label><br>
       <table id="searchObjResults">
         <tr><th>Name</th><th>Phone</th></tr>
         <tr ng-repeat="friendObj in friends | filter:search:strict">
           <td>{{friendObj.name}}</td>
           <td>{{friendObj.phone}}</td>
         </tr>
       </table>
     </file>
     <file name="protractor.js" type="protractor">
       var expectFriendNames = function(expectedNames, key) {
         element.all(by.repeater(key + ' in friends').column(key + '.name')).then(function(arr) {
           arr.forEach(function(wd, i) {
             expect(wd.getText()).toMatch(expectedNames[i]);
           });
         });
       };

       it('should search across all fields when filtering with a string', function() {
         var searchText = element(by.model('searchText'));
         searchText.clear();
         searchText.sendKeys('m');
         expectFriendNames(['Mary', 'Mike', 'Adam'], 'friend');

         searchText.clear();
         searchText.sendKeys('76');
         expectFriendNames(['John', 'Julie'], 'friend');
       });

       it('should search in specific fields when filtering with a predicate object', function() {
         var searchAny = element(by.model('search.$'));
         searchAny.clear();
         searchAny.sendKeys('i');
         expectFriendNames(['Mary', 'Mike', 'Julie', 'Juliette'], 'friendObj');
       });
       it('should use a equal comparison when comparator is true', function() {
         var searchName = element(by.model('search.name'));
         var strict = element(by.model('strict'));
         searchName.clear();
         searchName.sendKeys('Julie');
         strict.click();
         expectFriendNames(['Julie'], 'friendObj');
       });
     </file>
   </example>
 */

function filterFilter() {
  return function(array, expression, comparator, anyPropertyKey) {
    if (!isArrayLike(array)) {
      if (array == null) {
        return array;
      } else {
        throw minErr('filter')('notarray', 'Expected array but received: {0}', array);
      }
    }

    anyPropertyKey = anyPropertyKey || '$';
    var expressionType = getTypeForFilter(expression);
    var predicateFn;
    var matchAgainstAnyProp;

    switch (expressionType) {
      case 'function':
        predicateFn = expression;
        break;
      case 'boolean':
      case 'null':
      case 'number':
      case 'string':
        matchAgainstAnyProp = true;
        // falls through
      case 'object':
        predicateFn = createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp);
        break;
      default:
        return array;
    }

    return Array.prototype.filter.call(array, predicateFn);
  };
}

// Helper functions for `filterFilter`
function createPredicateFn(expression, comparator, anyPropertyKey, matchAgainstAnyProp) {
  var shouldMatchPrimitives = isObject(expression) && (anyPropertyKey in expression);
  var predicateFn;

  if (comparator === true) {
    comparator = equals;
  } else if (!isFunction(comparator)) {
    comparator = function(actual, expected) {
      if (isUndefined(actual)) {
        // No substring matching against `undefined`
        return false;
      }
      if ((actual === null) || (expected === null)) {
        // No substring matching against `null`; only match against `null`
        return actual === expected;
      }
      if (isObject(expected) || (isObject(actual) && !hasCustomToString(actual))) {
        // Should not compare primitives against objects, unless they have custom `toString` method
        return false;
      }

      actual = lowercase('' + actual);
      expected = lowercase('' + expected);
      return actual.indexOf(expected) !== -1;
    };
  }

  predicateFn = function(item) {
    if (shouldMatchPrimitives && !isObject(item)) {
      return deepCompare(item, expression[anyPropertyKey], comparator, anyPropertyKey, false);
    }
    return deepCompare(item, expression, comparator, anyPropertyKey, matchAgainstAnyProp);
  };

  return predicateFn;
}

function deepCompare(actual, expected, comparator, anyPropertyKey, matchAgainstAnyProp, dontMatchWholeObject) {
  var actualType = getTypeForFilter(actual);
  var expectedType = getTypeForFilter(expected);

  if ((expectedType === 'string') && (expected.charAt(0) === '!')) {
    return !deepCompare(actual, expected.substring(1), comparator, anyPropertyKey, matchAgainstAnyProp);
  } else if (isArray(actual)) {
    // In case `actual` is an array, consider it a match
    // if ANY of it's items matches `expected`
    return actual.some(function(item) {
      return deepCompare(item, expected, comparator, anyPropertyKey, matchAgainstAnyProp);
    });
  }

  switch (actualType) {
    case 'object':
      var key;
      if (matchAgainstAnyProp) {
        for (key in actual) {
          // Under certain, rare, circumstances, key may not be a string and `charAt` will be undefined
          // See: https://github.com/angular/angular.js/issues/15644
          if (key.charAt && (key.charAt(0) !== '$') &&
              deepCompare(actual[key], expected, comparator, anyPropertyKey, true)) {
            return true;
          }
        }
        return dontMatchWholeObject ? false : deepCompare(actual, expected, comparator, anyPropertyKey, false);
      } else if (expectedType === 'object') {
        for (key in expected) {
          var expectedVal = expected[key];
          if (isFunction(expectedVal) || isUndefined(expectedVal)) {
            continue;
          }

          var matchAnyProperty = key === anyPropertyKey;
          var actualVal = matchAnyProperty ? actual : actual[key];
          if (!deepCompare(actualVal, expectedVal, comparator, anyPropertyKey, matchAnyProperty, matchAnyProperty)) {
            return false;
          }
        }
        return true;
      } else {
        return comparator(actual, expected);
      }
    case 'function':
      return false;
    default:
      return comparator(actual, expected);
  }
}

// Used for easily differentiating between `null` and actual `object`
function getTypeForFilter(val) {
  return (val === null) ? 'null' : typeof val;
}

var MAX_DIGITS = 22;
var DECIMAL_SEP = '.';
var ZERO_CHAR = '0';

/**
 * @ngdoc filter
 * @name currency
 * @kind function
 *
 * @description
 * Formats a number as a currency (ie $1,234.56). When no currency symbol is provided, default
 * symbol for current locale is used.
 *
 * @param {number} amount Input to filter.
 * @param {string=} symbol Currency symbol or identifier to be displayed.
 * @param {number=} fractionSize Number of decimal places to round the amount to, defaults to default max fraction size for current locale
 * @returns {string} Formatted number.
 *
 *
 * @example
   <example module="currencyExample" name="currency-filter">
     <file name="index.html">
       <script>
         angular.module('currencyExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.amount = 1234.56;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <input type="number" ng-model="amount" aria-label="amount"> <br>
         default currency symbol ($): <span id="currency-default">{{amount | currency}}</span><br>
         custom currency identifier (USD$): <span id="currency-custom">{{amount | currency:"USD$"}}</span><br>
         no fractions (0): <span id="currency-no-fractions">{{amount | currency:"USD$":0}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should init with 1234.56', function() {
         expect(element(by.id('currency-default')).getText()).toBe('$1,234.56');
         expect(element(by.id('currency-custom')).getText()).toBe('USD$1,234.56');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('USD$1,235');
       });
       it('should update', function() {
         if (browser.params.browser === 'safari') {
           // Safari does not understand the minus key. See
           // https://github.com/angular/protractor/issues/481
           return;
         }
         element(by.model('amount')).clear();
         element(by.model('amount')).sendKeys('-1234');
         expect(element(by.id('currency-default')).getText()).toBe('-$1,234.00');
         expect(element(by.id('currency-custom')).getText()).toBe('-USD$1,234.00');
         expect(element(by.id('currency-no-fractions')).getText()).toBe('-USD$1,234');
       });
     </file>
   </example>
 */
currencyFilter.$inject = ['$locale'];
function currencyFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(amount, currencySymbol, fractionSize) {
    if (isUndefined(currencySymbol)) {
      currencySymbol = formats.CURRENCY_SYM;
    }

    if (isUndefined(fractionSize)) {
      fractionSize = formats.PATTERNS[1].maxFrac;
    }

    // if null or undefined pass it through
    return (amount == null)
        ? amount
        : formatNumber(amount, formats.PATTERNS[1], formats.GROUP_SEP, formats.DECIMAL_SEP, fractionSize).
            replace(/\u00A4/g, currencySymbol);
  };
}

/**
 * @ngdoc filter
 * @name number
 * @kind function
 *
 * @description
 * Formats a number as text.
 *
 * If the input is null or undefined, it will just be returned.
 * If the input is infinite (Infinity or -Infinity), the Infinity symbol '∞' or '-∞' is returned, respectively.
 * If the input is not a number an empty string is returned.
 *
 *
 * @param {number|string} number Number to format.
 * @param {(number|string)=} fractionSize Number of decimal places to round the number to.
 * If this is not provided then the fraction size is computed from the current locale's number
 * formatting pattern. In the case of the default locale, it will be 3.
 * @returns {string} Number rounded to `fractionSize` appropriately formatted based on the current
 *                   locale (e.g., in the en_US locale it will have "." as the decimal separator and
 *                   include "," group separators after each third digit).
 *
 * @example
   <example module="numberFilterExample" name="number-filter">
     <file name="index.html">
       <script>
         angular.module('numberFilterExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.val = 1234.56789;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter number: <input ng-model='val'></label><br>
         Default formatting: <span id='number-default'>{{val | number}}</span><br>
         No fractions: <span>{{val | number:0}}</span><br>
         Negative number: <span>{{-val | number:4}}</span>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format numbers', function() {
         expect(element(by.id('number-default')).getText()).toBe('1,234.568');
         expect(element(by.binding('val | number:0')).getText()).toBe('1,235');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-1,234.5679');
       });

       it('should update', function() {
         element(by.model('val')).clear();
         element(by.model('val')).sendKeys('3374.333');
         expect(element(by.id('number-default')).getText()).toBe('3,374.333');
         expect(element(by.binding('val | number:0')).getText()).toBe('3,374');
         expect(element(by.binding('-val | number:4')).getText()).toBe('-3,374.3330');
      });
     </file>
   </example>
 */
numberFilter.$inject = ['$locale'];
function numberFilter($locale) {
  var formats = $locale.NUMBER_FORMATS;
  return function(number, fractionSize) {

    // if null or undefined pass it through
    return (number == null)
        ? number
        : formatNumber(number, formats.PATTERNS[0], formats.GROUP_SEP, formats.DECIMAL_SEP,
                       fractionSize);
  };
}

/**
 * Parse a number (as a string) into three components that can be used
 * for formatting the number.
 *
 * (Significant bits of this parse algorithm came from https://github.com/MikeMcl/big.js/)
 *
 * @param  {string} numStr The number to parse
 * @return {object} An object describing this number, containing the following keys:
 *  - d : an array of digits containing leading zeros as necessary
 *  - i : the number of the digits in `d` that are to the left of the decimal point
 *  - e : the exponent for numbers that would need more than `MAX_DIGITS` digits in `d`
 *
 */
function parse(numStr) {
  var exponent = 0, digits, numberOfIntegerDigits;
  var i, j, zeros;

  // Decimal point?
  if ((numberOfIntegerDigits = numStr.indexOf(DECIMAL_SEP)) > -1) {
    numStr = numStr.replace(DECIMAL_SEP, '');
  }

  // Exponential form?
  if ((i = numStr.search(/e/i)) > 0) {
    // Work out the exponent.
    if (numberOfIntegerDigits < 0) numberOfIntegerDigits = i;
    numberOfIntegerDigits += +numStr.slice(i + 1);
    numStr = numStr.substring(0, i);
  } else if (numberOfIntegerDigits < 0) {
    // There was no decimal point or exponent so it is an integer.
    numberOfIntegerDigits = numStr.length;
  }

  // Count the number of leading zeros.
  for (i = 0; numStr.charAt(i) === ZERO_CHAR; i++) { /* empty */ }

  if (i === (zeros = numStr.length)) {
    // The digits are all zero.
    digits = [0];
    numberOfIntegerDigits = 1;
  } else {
    // Count the number of trailing zeros
    zeros--;
    while (numStr.charAt(zeros) === ZERO_CHAR) zeros--;

    // Trailing zeros are insignificant so ignore them
    numberOfIntegerDigits -= i;
    digits = [];
    // Convert string to array of digits without leading/trailing zeros.
    for (j = 0; i <= zeros; i++, j++) {
      digits[j] = +numStr.charAt(i);
    }
  }

  // If the number overflows the maximum allowed digits then use an exponent.
  if (numberOfIntegerDigits > MAX_DIGITS) {
    digits = digits.splice(0, MAX_DIGITS - 1);
    exponent = numberOfIntegerDigits - 1;
    numberOfIntegerDigits = 1;
  }

  return { d: digits, e: exponent, i: numberOfIntegerDigits };
}

/**
 * Round the parsed number to the specified number of decimal places
 * This function changed the parsedNumber in-place
 */
function roundNumber(parsedNumber, fractionSize, minFrac, maxFrac) {
    var digits = parsedNumber.d;
    var fractionLen = digits.length - parsedNumber.i;

    // determine fractionSize if it is not specified; `+fractionSize` converts it to a number
    fractionSize = (isUndefined(fractionSize)) ? Math.min(Math.max(minFrac, fractionLen), maxFrac) : +fractionSize;

    // The index of the digit to where rounding is to occur
    var roundAt = fractionSize + parsedNumber.i;
    var digit = digits[roundAt];

    if (roundAt > 0) {
      // Drop fractional digits beyond `roundAt`
      digits.splice(Math.max(parsedNumber.i, roundAt));

      // Set non-fractional digits beyond `roundAt` to 0
      for (var j = roundAt; j < digits.length; j++) {
        digits[j] = 0;
      }
    } else {
      // We rounded to zero so reset the parsedNumber
      fractionLen = Math.max(0, fractionLen);
      parsedNumber.i = 1;
      digits.length = Math.max(1, roundAt = fractionSize + 1);
      digits[0] = 0;
      for (var i = 1; i < roundAt; i++) digits[i] = 0;
    }

    if (digit >= 5) {
      if (roundAt - 1 < 0) {
        for (var k = 0; k > roundAt; k--) {
          digits.unshift(0);
          parsedNumber.i++;
        }
        digits.unshift(1);
        parsedNumber.i++;
      } else {
        digits[roundAt - 1]++;
      }
    }

    // Pad out with zeros to get the required fraction length
    for (; fractionLen < Math.max(0, fractionSize); fractionLen++) digits.push(0);


    // Do any carrying, e.g. a digit was rounded up to 10
    var carry = digits.reduceRight(function(carry, d, i, digits) {
      d = d + carry;
      digits[i] = d % 10;
      return Math.floor(d / 10);
    }, 0);
    if (carry) {
      digits.unshift(carry);
      parsedNumber.i++;
    }
}

/**
 * Format a number into a string
 * @param  {number} number       The number to format
 * @param  {{
 *           minFrac, // the minimum number of digits required in the fraction part of the number
 *           maxFrac, // the maximum number of digits required in the fraction part of the number
 *           gSize,   // number of digits in each group of separated digits
 *           lgSize,  // number of digits in the last group of digits before the decimal separator
 *           negPre,  // the string to go in front of a negative number (e.g. `-` or `(`))
 *           posPre,  // the string to go in front of a positive number
 *           negSuf,  // the string to go after a negative number (e.g. `)`)
 *           posSuf   // the string to go after a positive number
 *         }} pattern
 * @param  {string} groupSep     The string to separate groups of number (e.g. `,`)
 * @param  {string} decimalSep   The string to act as the decimal separator (e.g. `.`)
 * @param  {[type]} fractionSize The size of the fractional part of the number
 * @return {string}              The number formatted as a string
 */
function formatNumber(number, pattern, groupSep, decimalSep, fractionSize) {

  if (!(isString(number) || isNumber(number)) || isNaN(number)) return '';

  var isInfinity = !isFinite(number);
  var isZero = false;
  var numStr = Math.abs(number) + '',
      formattedText = '',
      parsedNumber;

  if (isInfinity) {
    formattedText = '\u221e';
  } else {
    parsedNumber = parse(numStr);

    roundNumber(parsedNumber, fractionSize, pattern.minFrac, pattern.maxFrac);

    var digits = parsedNumber.d;
    var integerLen = parsedNumber.i;
    var exponent = parsedNumber.e;
    var decimals = [];
    isZero = digits.reduce(function(isZero, d) { return isZero && !d; }, true);

    // pad zeros for small numbers
    while (integerLen < 0) {
      digits.unshift(0);
      integerLen++;
    }

    // extract decimals digits
    if (integerLen > 0) {
      decimals = digits.splice(integerLen, digits.length);
    } else {
      decimals = digits;
      digits = [0];
    }

    // format the integer digits with grouping separators
    var groups = [];
    if (digits.length >= pattern.lgSize) {
      groups.unshift(digits.splice(-pattern.lgSize, digits.length).join(''));
    }
    while (digits.length > pattern.gSize) {
      groups.unshift(digits.splice(-pattern.gSize, digits.length).join(''));
    }
    if (digits.length) {
      groups.unshift(digits.join(''));
    }
    formattedText = groups.join(groupSep);

    // append the decimal digits
    if (decimals.length) {
      formattedText += decimalSep + decimals.join('');
    }

    if (exponent) {
      formattedText += 'e+' + exponent;
    }
  }
  if (number < 0 && !isZero) {
    return pattern.negPre + formattedText + pattern.negSuf;
  } else {
    return pattern.posPre + formattedText + pattern.posSuf;
  }
}

function padNumber(num, digits, trim, negWrap) {
  var neg = '';
  if (num < 0 || (negWrap && num <= 0)) {
    if (negWrap) {
      num = -num + 1;
    } else {
      num = -num;
      neg = '-';
    }
  }
  num = '' + num;
  while (num.length < digits) num = ZERO_CHAR + num;
  if (trim) {
    num = num.substr(num.length - digits);
  }
  return neg + num;
}


function dateGetter(name, size, offset, trim, negWrap) {
  offset = offset || 0;
  return function(date) {
    var value = date['get' + name]();
    if (offset > 0 || value > -offset) {
      value += offset;
    }
    if (value === 0 && offset === -12) value = 12;
    return padNumber(value, size, trim, negWrap);
  };
}

function dateStrGetter(name, shortForm, standAlone) {
  return function(date, formats) {
    var value = date['get' + name]();
    var propPrefix = (standAlone ? 'STANDALONE' : '') + (shortForm ? 'SHORT' : '');
    var get = uppercase(propPrefix + name);

    return formats[get][value];
  };
}

function timeZoneGetter(date, formats, offset) {
  var zone = -1 * offset;
  var paddedZone = (zone >= 0) ? '+' : '';

  paddedZone += padNumber(Math[zone > 0 ? 'floor' : 'ceil'](zone / 60), 2) +
                padNumber(Math.abs(zone % 60), 2);

  return paddedZone;
}

function getFirstThursdayOfYear(year) {
    // 0 = index of January
    var dayOfWeekOnFirst = (new Date(year, 0, 1)).getDay();
    // 4 = index of Thursday (+1 to account for 1st = 5)
    // 11 = index of *next* Thursday (+1 account for 1st = 12)
    return new Date(year, 0, ((dayOfWeekOnFirst <= 4) ? 5 : 12) - dayOfWeekOnFirst);
}

function getThursdayThisWeek(datetime) {
    return new Date(datetime.getFullYear(), datetime.getMonth(),
      // 4 = index of Thursday
      datetime.getDate() + (4 - datetime.getDay()));
}

function weekGetter(size) {
   return function(date) {
      var firstThurs = getFirstThursdayOfYear(date.getFullYear()),
         thisThurs = getThursdayThisWeek(date);

      var diff = +thisThurs - +firstThurs,
         result = 1 + Math.round(diff / 6.048e8); // 6.048e8 ms per week

      return padNumber(result, size);
   };
}

function ampmGetter(date, formats) {
  return date.getHours() < 12 ? formats.AMPMS[0] : formats.AMPMS[1];
}

function eraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERAS[0] : formats.ERAS[1];
}

function longEraGetter(date, formats) {
  return date.getFullYear() <= 0 ? formats.ERANAMES[0] : formats.ERANAMES[1];
}

var DATE_FORMATS = {
  yyyy: dateGetter('FullYear', 4, 0, false, true),
    yy: dateGetter('FullYear', 2, 0, true, true),
     y: dateGetter('FullYear', 1, 0, false, true),
  MMMM: dateStrGetter('Month'),
   MMM: dateStrGetter('Month', true),
    MM: dateGetter('Month', 2, 1),
     M: dateGetter('Month', 1, 1),
  LLLL: dateStrGetter('Month', false, true),
    dd: dateGetter('Date', 2),
     d: dateGetter('Date', 1),
    HH: dateGetter('Hours', 2),
     H: dateGetter('Hours', 1),
    hh: dateGetter('Hours', 2, -12),
     h: dateGetter('Hours', 1, -12),
    mm: dateGetter('Minutes', 2),
     m: dateGetter('Minutes', 1),
    ss: dateGetter('Seconds', 2),
     s: dateGetter('Seconds', 1),
     // while ISO 8601 requires fractions to be prefixed with `.` or `,`
     // we can be just safely rely on using `sss` since we currently don't support single or two digit fractions
   sss: dateGetter('Milliseconds', 3),
  EEEE: dateStrGetter('Day'),
   EEE: dateStrGetter('Day', true),
     a: ampmGetter,
     Z: timeZoneGetter,
    ww: weekGetter(2),
     w: weekGetter(1),
     G: eraGetter,
     GG: eraGetter,
     GGG: eraGetter,
     GGGG: longEraGetter
};

var DATE_FORMATS_SPLIT = /((?:[^yMLdHhmsaZEwG']+)|(?:'(?:[^']|'')*')|(?:E+|y+|M+|L+|d+|H+|h+|m+|s+|a|Z|G+|w+))([\s\S]*)/,
    NUMBER_STRING = /^-?\d+$/;

/**
 * @ngdoc filter
 * @name date
 * @kind function
 *
 * @description
 *   Formats `date` to a string based on the requested `format`.
 *
 *   `format` string can be composed of the following elements:
 *
 *   * `'yyyy'`: 4 digit representation of year (e.g. AD 1 => 0001, AD 2010 => 2010)
 *   * `'yy'`: 2 digit representation of year, padded (00-99). (e.g. AD 2001 => 01, AD 2010 => 10)
 *   * `'y'`: 1 digit representation of year, e.g. (AD 1 => 1, AD 199 => 199)
 *   * `'MMMM'`: Month in year (January-December)
 *   * `'MMM'`: Month in year (Jan-Dec)
 *   * `'MM'`: Month in year, padded (01-12)
 *   * `'M'`: Month in year (1-12)
 *   * `'LLLL'`: Stand-alone month in year (January-December)
 *   * `'dd'`: Day in month, padded (01-31)
 *   * `'d'`: Day in month (1-31)
 *   * `'EEEE'`: Day in Week,(Sunday-Saturday)
 *   * `'EEE'`: Day in Week, (Sun-Sat)
 *   * `'HH'`: Hour in day, padded (00-23)
 *   * `'H'`: Hour in day (0-23)
 *   * `'hh'`: Hour in AM/PM, padded (01-12)
 *   * `'h'`: Hour in AM/PM, (1-12)
 *   * `'mm'`: Minute in hour, padded (00-59)
 *   * `'m'`: Minute in hour (0-59)
 *   * `'ss'`: Second in minute, padded (00-59)
 *   * `'s'`: Second in minute (0-59)
 *   * `'sss'`: Millisecond in second, padded (000-999)
 *   * `'a'`: AM/PM marker
 *   * `'Z'`: 4 digit (+sign) representation of the timezone offset (-1200-+1200)
 *   * `'ww'`: Week of year, padded (00-53). Week 01 is the week with the first Thursday of the year
 *   * `'w'`: Week of year (0-53). Week 1 is the week with the first Thursday of the year
 *   * `'G'`, `'GG'`, `'GGG'`: The abbreviated form of the era string (e.g. 'AD')
 *   * `'GGGG'`: The long form of the era string (e.g. 'Anno Domini')
 *
 *   `format` string can also be one of the following predefined
 *   {@link guide/i18n localizable formats}:
 *
 *   * `'medium'`: equivalent to `'MMM d, y h:mm:ss a'` for en_US locale
 *     (e.g. Sep 3, 2010 12:05:08 PM)
 *   * `'short'`: equivalent to `'M/d/yy h:mm a'` for en_US  locale (e.g. 9/3/10 12:05 PM)
 *   * `'fullDate'`: equivalent to `'EEEE, MMMM d, y'` for en_US  locale
 *     (e.g. Friday, September 3, 2010)
 *   * `'longDate'`: equivalent to `'MMMM d, y'` for en_US  locale (e.g. September 3, 2010)
 *   * `'mediumDate'`: equivalent to `'MMM d, y'` for en_US  locale (e.g. Sep 3, 2010)
 *   * `'shortDate'`: equivalent to `'M/d/yy'` for en_US locale (e.g. 9/3/10)
 *   * `'mediumTime'`: equivalent to `'h:mm:ss a'` for en_US locale (e.g. 12:05:08 PM)
 *   * `'shortTime'`: equivalent to `'h:mm a'` for en_US locale (e.g. 12:05 PM)
 *
 *   `format` string can contain literal values. These need to be escaped by surrounding with single quotes (e.g.
 *   `"h 'in the morning'"`). In order to output a single quote, escape it - i.e., two single quotes in a sequence
 *   (e.g. `"h 'o''clock'"`).
 *
 *   Any other characters in the `format` string will be output as-is.
 *
 * @param {(Date|number|string)} date Date to format either as Date object, milliseconds (string or
 *    number) or various ISO 8601 datetime string formats (e.g. yyyy-MM-ddTHH:mm:ss.sssZ and its
 *    shorter versions like yyyy-MM-ddTHH:mmZ, yyyy-MM-dd or yyyyMMddTHHmmssZ). If no timezone is
 *    specified in the string input, the time is considered to be in the local timezone.
 * @param {string=} format Formatting rules (see Description). If not specified,
 *    `mediumDate` is used.
 * @param {string=} timezone Timezone to be used for formatting. It understands UTC/GMT and the
 *    continental US time zone abbreviations, but for general use, use a time zone offset, for
 *    example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *    If not specified, the timezone of the browser will be used.
 * @returns {string} Formatted string or the input if input is not recognized as date/millis.
 *
 * @example
   <example name="filter-date">
     <file name="index.html">
       <span ng-non-bindable>{{1288323623006 | date:'medium'}}</span>:
           <span>{{1288323623006 | date:'medium'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span>:
          <span>{{1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:'MM/dd/yyyy @ h:mma'}}</span>:
          <span>{{'1288323623006' | date:'MM/dd/yyyy @ h:mma'}}</span><br>
       <span ng-non-bindable>{{1288323623006 | date:"MM/dd/yyyy 'at' h:mma"}}</span>:
          <span>{{'1288323623006' | date:"MM/dd/yyyy 'at' h:mma"}}</span><br>
     </file>
     <file name="protractor.js" type="protractor">
       it('should format date', function() {
         expect(element(by.binding("1288323623006 | date:'medium'")).getText()).
            toMatch(/Oct 2\d, 2010 \d{1,2}:\d{2}:\d{2} (AM|PM)/);
         expect(element(by.binding("1288323623006 | date:'yyyy-MM-dd HH:mm:ss Z'")).getText()).
            toMatch(/2010-10-2\d \d{2}:\d{2}:\d{2} (-|\+)?\d{4}/);
         expect(element(by.binding("'1288323623006' | date:'MM/dd/yyyy @ h:mma'")).getText()).
            toMatch(/10\/2\d\/2010 @ \d{1,2}:\d{2}(AM|PM)/);
         expect(element(by.binding("'1288323623006' | date:\"MM/dd/yyyy 'at' h:mma\"")).getText()).
            toMatch(/10\/2\d\/2010 at \d{1,2}:\d{2}(AM|PM)/);
       });
     </file>
   </example>
 */
dateFilter.$inject = ['$locale'];
function dateFilter($locale) {


  var R_ISO8601_STR = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
                     // 1        2       3         4          5          6          7          8  9     10      11
  function jsonStringToDate(string) {
    var match;
    if ((match = string.match(R_ISO8601_STR))) {
      var date = new Date(0),
          tzHour = 0,
          tzMin  = 0,
          dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear,
          timeSetter = match[8] ? date.setUTCHours : date.setHours;

      if (match[9]) {
        tzHour = toInt(match[9] + match[10]);
        tzMin = toInt(match[9] + match[11]);
      }
      dateSetter.call(date, toInt(match[1]), toInt(match[2]) - 1, toInt(match[3]));
      var h = toInt(match[4] || 0) - tzHour;
      var m = toInt(match[5] || 0) - tzMin;
      var s = toInt(match[6] || 0);
      var ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
      timeSetter.call(date, h, m, s, ms);
      return date;
    }
    return string;
  }


  return function(date, format, timezone) {
    var text = '',
        parts = [],
        fn, match;

    format = format || 'mediumDate';
    format = $locale.DATETIME_FORMATS[format] || format;
    if (isString(date)) {
      date = NUMBER_STRING.test(date) ? toInt(date) : jsonStringToDate(date);
    }

    if (isNumber(date)) {
      date = new Date(date);
    }

    if (!isDate(date) || !isFinite(date.getTime())) {
      return date;
    }

    while (format) {
      match = DATE_FORMATS_SPLIT.exec(format);
      if (match) {
        parts = concat(parts, match, 1);
        format = parts.pop();
      } else {
        parts.push(format);
        format = null;
      }
    }

    var dateTimezoneOffset = date.getTimezoneOffset();
    if (timezone) {
      dateTimezoneOffset = timezoneToOffset(timezone, dateTimezoneOffset);
      date = convertTimezoneToLocal(date, timezone, true);
    }
    forEach(parts, function(value) {
      fn = DATE_FORMATS[value];
      text += fn ? fn(date, $locale.DATETIME_FORMATS, dateTimezoneOffset)
                 : value === '\'\'' ? '\'' : value.replace(/(^'|'$)/g, '').replace(/''/g, '\'');
    });

    return text;
  };
}


/**
 * @ngdoc filter
 * @name json
 * @kind function
 *
 * @description
 *   Allows you to convert a JavaScript object into JSON string.
 *
 *   This filter is mostly useful for debugging. When using the double curly {{value}} notation
 *   the binding is automatically converted to JSON.
 *
 * @param {*} object Any JavaScript object (including arrays and primitive types) to filter.
 * @param {number=} spacing The number of spaces to use per indentation, defaults to 2.
 * @returns {string} JSON string.
 *
 *
 * @example
   <example name="filter-json">
     <file name="index.html">
       <pre id="default-spacing">{{ {'name':'value'} | json }}</pre>
       <pre id="custom-spacing">{{ {'name':'value'} | json:4 }}</pre>
     </file>
     <file name="protractor.js" type="protractor">
       it('should jsonify filtered objects', function() {
         expect(element(by.id('default-spacing')).getText()).toMatch(/\{\n {2}"name": ?"value"\n}/);
         expect(element(by.id('custom-spacing')).getText()).toMatch(/\{\n {4}"name": ?"value"\n}/);
       });
     </file>
   </example>
 *
 */
function jsonFilter() {
  return function(object, spacing) {
    if (isUndefined(spacing)) {
        spacing = 2;
    }
    return toJson(object, spacing);
  };
}


/**
 * @ngdoc filter
 * @name lowercase
 * @kind function
 * @description
 * Converts string to lowercase.
 *
 * See the {@link ng.uppercase uppercase filter documentation} for a functionally identical example.
 *
 * @see angular.lowercase
 */
var lowercaseFilter = valueFn(lowercase);


/**
 * @ngdoc filter
 * @name uppercase
 * @kind function
 * @description
 * Converts string to uppercase.
 * @example
   <example module="uppercaseFilterExample" name="filter-uppercase">
     <file name="index.html">
       <script>
         angular.module('uppercaseFilterExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.title = 'This is a title';
           }]);
       </script>
       <div ng-controller="ExampleController">
         <!-- This title should be formatted normally -->
         <h1>{{title}}</h1>
         <!-- This title should be capitalized -->
         <h1>{{title | uppercase}}</h1>
       </div>
     </file>
   </example>
 */
var uppercaseFilter = valueFn(uppercase);

/**
 * @ngdoc filter
 * @name limitTo
 * @kind function
 *
 * @description
 * Creates a new array or string containing only a specified number of elements. The elements are
 * taken from either the beginning or the end of the source array, string or number, as specified by
 * the value and sign (positive or negative) of `limit`. Other array-like objects are also supported
 * (e.g. array subclasses, NodeLists, jqLite/jQuery collections etc). If a number is used as input,
 * it is converted to a string.
 *
 * @param {Array|ArrayLike|string|number} input - Array/array-like, string or number to be limited.
 * @param {string|number} limit - The length of the returned array or string. If the `limit` number
 *     is positive, `limit` number of items from the beginning of the source array/string are copied.
 *     If the number is negative, `limit` number  of items from the end of the source array/string
 *     are copied. The `limit` will be trimmed if it exceeds `array.length`. If `limit` is undefined,
 *     the input will be returned unchanged.
 * @param {(string|number)=} begin - Index at which to begin limitation. As a negative index,
 *     `begin` indicates an offset from the end of `input`. Defaults to `0`.
 * @returns {Array|string} A new sub-array or substring of length `limit` or less if the input had
 *     less than `limit` elements.
 *
 * @example
   <example module="limitToExample" name="limit-to-filter">
     <file name="index.html">
       <script>
         angular.module('limitToExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.numbers = [1,2,3,4,5,6,7,8,9];
             $scope.letters = "abcdefghi";
             $scope.longNumber = 2345432342;
             $scope.numLimit = 3;
             $scope.letterLimit = 3;
             $scope.longNumberLimit = 3;
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>
            Limit {{numbers}} to:
            <input type="number" step="1" ng-model="numLimit">
         </label>
         <p>Output numbers: {{ numbers | limitTo:numLimit }}</p>
         <label>
            Limit {{letters}} to:
            <input type="number" step="1" ng-model="letterLimit">
         </label>
         <p>Output letters: {{ letters | limitTo:letterLimit }}</p>
         <label>
            Limit {{longNumber}} to:
            <input type="number" step="1" ng-model="longNumberLimit">
         </label>
         <p>Output long number: {{ longNumber | limitTo:longNumberLimit }}</p>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       var numLimitInput = element(by.model('numLimit'));
       var letterLimitInput = element(by.model('letterLimit'));
       var longNumberLimitInput = element(by.model('longNumberLimit'));
       var limitedNumbers = element(by.binding('numbers | limitTo:numLimit'));
       var limitedLetters = element(by.binding('letters | limitTo:letterLimit'));
       var limitedLongNumber = element(by.binding('longNumber | limitTo:longNumberLimit'));

       it('should limit the number array to first three items', function() {
         expect(numLimitInput.getAttribute('value')).toBe('3');
         expect(letterLimitInput.getAttribute('value')).toBe('3');
         expect(longNumberLimitInput.getAttribute('value')).toBe('3');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3]');
         expect(limitedLetters.getText()).toEqual('Output letters: abc');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 234');
       });

       // There is a bug in safari and protractor that doesn't like the minus key
       // it('should update the output when -3 is entered', function() {
       //   numLimitInput.clear();
       //   numLimitInput.sendKeys('-3');
       //   letterLimitInput.clear();
       //   letterLimitInput.sendKeys('-3');
       //   longNumberLimitInput.clear();
       //   longNumberLimitInput.sendKeys('-3');
       //   expect(limitedNumbers.getText()).toEqual('Output numbers: [7,8,9]');
       //   expect(limitedLetters.getText()).toEqual('Output letters: ghi');
       //   expect(limitedLongNumber.getText()).toEqual('Output long number: 342');
       // });

       it('should not exceed the maximum size of input array', function() {
         numLimitInput.clear();
         numLimitInput.sendKeys('100');
         letterLimitInput.clear();
         letterLimitInput.sendKeys('100');
         longNumberLimitInput.clear();
         longNumberLimitInput.sendKeys('100');
         expect(limitedNumbers.getText()).toEqual('Output numbers: [1,2,3,4,5,6,7,8,9]');
         expect(limitedLetters.getText()).toEqual('Output letters: abcdefghi');
         expect(limitedLongNumber.getText()).toEqual('Output long number: 2345432342');
       });
     </file>
   </example>
*/
function limitToFilter() {
  return function(input, limit, begin) {
    if (Math.abs(Number(limit)) === Infinity) {
      limit = Number(limit);
    } else {
      limit = toInt(limit);
    }
    if (isNumberNaN(limit)) return input;

    if (isNumber(input)) input = input.toString();
    if (!isArrayLike(input)) return input;

    begin = (!begin || isNaN(begin)) ? 0 : toInt(begin);
    begin = (begin < 0) ? Math.max(0, input.length + begin) : begin;

    if (limit >= 0) {
      return sliceFn(input, begin, begin + limit);
    } else {
      if (begin === 0) {
        return sliceFn(input, limit, input.length);
      } else {
        return sliceFn(input, Math.max(0, begin + limit), begin);
      }
    }
  };
}

function sliceFn(input, begin, end) {
  if (isString(input)) return input.slice(begin, end);

  return slice.call(input, begin, end);
}

/**
 * @ngdoc filter
 * @name orderBy
 * @kind function
 *
 * @description
 * Returns an array containing the items from the specified `collection`, ordered by a `comparator`
 * function based on the values computed using the `expression` predicate.
 *
 * For example, `[{id: 'foo'}, {id: 'bar'}] | orderBy:'id'` would result in
 * `[{id: 'bar'}, {id: 'foo'}]`.
 *
 * The `collection` can be an Array or array-like object (e.g. NodeList, jQuery object, TypedArray,
 * String, etc).
 *
 * The `expression` can be a single predicate, or a list of predicates each serving as a tie-breaker
 * for the preceding one. The `expression` is evaluated against each item and the output is used
 * for comparing with other items.
 *
 * You can change the sorting order by setting `reverse` to `true`. By default, items are sorted in
 * ascending order.
 *
 * The comparison is done using the `comparator` function. If none is specified, a default, built-in
 * comparator is used (see below for details - in a nutshell, it compares numbers numerically and
 * strings alphabetically).
 *
 * ### Under the hood
 *
 * Ordering the specified `collection` happens in two phases:
 *
 * 1. All items are passed through the predicate (or predicates), and the returned values are saved
 *    along with their type (`string`, `number` etc). For example, an item `{label: 'foo'}`, passed
 *    through a predicate that extracts the value of the `label` property, would be transformed to:
 *    ```
 *    {
 *      value: 'foo',
 *      type: 'string',
 *      index: ...
 *    }
 *    ```
 * 2. The comparator function is used to sort the items, based on the derived values, types and
 *    indices.
 *
 * If you use a custom comparator, it will be called with pairs of objects of the form
 * `{value: ..., type: '...', index: ...}` and is expected to return `0` if the objects are equal
 * (as far as the comparator is concerned), `-1` if the 1st one should be ranked higher than the
 * second, or `1` otherwise.
 *
 * In order to ensure that the sorting will be deterministic across platforms, if none of the
 * specified predicates can distinguish between two items, `orderBy` will automatically introduce a
 * dummy predicate that returns the item's index as `value`.
 * (If you are using a custom comparator, make sure it can handle this predicate as well.)
 *
 * If a custom comparator still can't distinguish between two items, then they will be sorted based
 * on their index using the built-in comparator.
 *
 * Finally, in an attempt to simplify things, if a predicate returns an object as the extracted
 * value for an item, `orderBy` will try to convert that object to a primitive value, before passing
 * it to the comparator. The following rules govern the conversion:
 *
 * 1. If the object has a `valueOf()` method that returns a primitive, its return value will be
 *    used instead.<br />
 *    (If the object has a `valueOf()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 2. If the object has a custom `toString()` method (i.e. not the one inherited from `Object`) that
 *    returns a primitive, its return value will be used instead.<br />
 *    (If the object has a `toString()` method that returns another object, then the returned object
 *    will be used in subsequent steps.)
 * 3. No conversion; the object itself is used.
 *
 * ### The default comparator
 *
 * The default, built-in comparator should be sufficient for most usecases. In short, it compares
 * numbers numerically, strings alphabetically (and case-insensitively), for objects falls back to
 * using their index in the original collection, and sorts values of different types by type.
 *
 * More specifically, it follows these steps to determine the relative order of items:
 *
 * 1. If the compared values are of different types, compare the types themselves alphabetically.
 * 2. If both values are of type `string`, compare them alphabetically in a case- and
 *    locale-insensitive way.
 * 3. If both values are objects, compare their indices instead.
 * 4. Otherwise, return:
 *    -  `0`, if the values are equal (by strict equality comparison, i.e. using `===`).
 *    - `-1`, if the 1st value is "less than" the 2nd value (compared using the `<` operator).
 *    -  `1`, otherwise.
 *
 * **Note:** If you notice numbers not being sorted as expected, make sure they are actually being
 *           saved as numbers and not strings.
 * **Note:** For the purpose of sorting, `null` values are treated as the string `'null'` (i.e.
 *           `type: 'string'`, `value: 'null'`). This may cause unexpected sort order relative to
 *           other values.
 *
 * @param {Array|ArrayLike} collection - The collection (array or array-like object) to sort.
 * @param {(Function|string|Array.<Function|string>)=} expression - A predicate (or list of
 *    predicates) to be used by the comparator to determine the order of elements.
 *
 *    Can be one of:
 *
 *    - `Function`: A getter function. This function will be called with each item as argument and
 *      the return value will be used for sorting.
 *    - `string`: An Angular expression. This expression will be evaluated against each item and the
 *      result will be used for sorting. For example, use `'label'` to sort by a property called
 *      `label` or `'label.substring(0, 3)'` to sort by the first 3 characters of the `label`
 *      property.<br />
 *      (The result of a constant expression is interpreted as a property name to be used for
 *      comparison. For example, use `'"special name"'` (note the extra pair of quotes) to sort by a
 *      property called `special name`.)<br />
 *      An expression can be optionally prefixed with `+` or `-` to control the sorting direction,
 *      ascending or descending. For example, `'+label'` or `'-label'`. If no property is provided,
 *      (e.g. `'+'` or `'-'`), the collection element itself is used in comparisons.
 *    - `Array`: An array of function and/or string predicates. If a predicate cannot determine the
 *      relative order of two items, the next predicate is used as a tie-breaker.
 *
 * **Note:** If the predicate is missing or empty then it defaults to `'+'`.
 *
 * @param {boolean=} reverse - If `true`, reverse the sorting order.
 * @param {(Function)=} comparator - The comparator function used to determine the relative order of
 *    value pairs. If omitted, the built-in comparator will be used.
 *
 * @returns {Array} - The sorted array.
 *
 *
 * @example
 * ### Ordering a table with `ngRepeat`
 *
 * The example below demonstrates a simple {@link ngRepeat ngRepeat}, where the data is sorted by
 * age in descending order (expression is set to `'-age'`). The `comparator` is not set, which means
 * it defaults to the built-in comparator.
 *
   <example name="orderBy-static" module="orderByExample1">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <table class="friends">
           <tr>
             <th>Name</th>
             <th>Phone Number</th>
             <th>Age</th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:'-age'">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample1', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var names = element.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by age in reverse order', function() {
         expect(names.get(0).getText()).toBe('Adam');
         expect(names.get(1).getText()).toBe('Julie');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('John');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Changing parameters dynamically
 *
 * All parameters can be changed dynamically. The next example shows how you can make the columns of
 * a table sortable, by binding the `expression` and `reverse` parameters to scope properties.
 *
   <example name="orderBy-dynamic" module="orderByExample2">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="propertyName = null; reverse = false">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends | orderBy:propertyName:reverse">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample2', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = friends;

           $scope.sortBy = function(propertyName) {
             $scope.reverse = ($scope.propertyName === propertyName) ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using `orderBy` inside a controller
 *
 * It is also possible to call the `orderBy` filter manually, by injecting `orderByFilter`, and
 * calling it with the desired parameters. (Alternatively, you could inject the `$filter` factory
 * and retrieve the `orderBy` filter with `$filter('orderBy')`.)
 *
   <example name="orderBy-call-manually" module="orderByExample3">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <pre>Sort by = {{propertyName}}; reverse = {{reverse}}</pre>
         <hr/>
         <button ng-click="sortBy(null)">Set to unsorted</button>
         <hr/>
         <table class="friends">
           <tr>
             <th>
               <button ng-click="sortBy('name')">Name</button>
               <span class="sortorder" ng-show="propertyName === 'name'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('phone')">Phone Number</button>
               <span class="sortorder" ng-show="propertyName === 'phone'" ng-class="{reverse: reverse}"></span>
             </th>
             <th>
               <button ng-click="sortBy('age')">Age</button>
               <span class="sortorder" ng-show="propertyName === 'age'" ng-class="{reverse: reverse}"></span>
             </th>
           </tr>
           <tr ng-repeat="friend in friends">
             <td>{{friend.name}}</td>
             <td>{{friend.phone}}</td>
             <td>{{friend.age}}</td>
           </tr>
         </table>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample3', [])
         .controller('ExampleController', ['$scope', 'orderByFilter', function($scope, orderBy) {
           var friends = [
             {name: 'John',   phone: '555-1212',  age: 10},
             {name: 'Mary',   phone: '555-9876',  age: 19},
             {name: 'Mike',   phone: '555-4321',  age: 21},
             {name: 'Adam',   phone: '555-5678',  age: 35},
             {name: 'Julie',  phone: '555-8765',  age: 29}
           ];

           $scope.propertyName = 'age';
           $scope.reverse = true;
           $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);

           $scope.sortBy = function(propertyName) {
             $scope.reverse = (propertyName !== null && $scope.propertyName === propertyName)
                 ? !$scope.reverse : false;
             $scope.propertyName = propertyName;
             $scope.friends = orderBy(friends, $scope.propertyName, $scope.reverse);
           };
         }]);
     </file>
     <file name="style.css">
       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }

       .sortorder:after {
         content: '\25b2';   // BLACK UP-POINTING TRIANGLE
       }
       .sortorder.reverse:after {
         content: '\25bc';   // BLACK DOWN-POINTING TRIANGLE
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var unsortButton = element(by.partialButtonText('unsorted'));
       var nameHeader = element(by.partialButtonText('Name'));
       var phoneHeader = element(by.partialButtonText('Phone'));
       var ageHeader = element(by.partialButtonText('Age'));
       var firstName = element(by.repeater('friends').column('friend.name').row(0));
       var lastName = element(by.repeater('friends').column('friend.name').row(4));

       it('should sort friends by some property, when clicking on the column header', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         phoneHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Mary');

         nameHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('Mike');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');
       });

       it('should sort friends in reverse order, when clicking on the same column', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         ageHeader.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Adam');

         ageHeader.click();
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');
       });

       it('should restore the original order, when clicking "Set to unsorted"', function() {
         expect(firstName.getText()).toBe('Adam');
         expect(lastName.getText()).toBe('John');

         unsortButton.click();
         expect(firstName.getText()).toBe('John');
         expect(lastName.getText()).toBe('Julie');
       });
     </file>
   </example>
 * <hr />
 *
 * @example
 * ### Using a custom comparator
 *
 * If you have very specific requirements about the way items are sorted, you can pass your own
 * comparator function. For example, you might need to compare some strings in a locale-sensitive
 * way. (When specifying a custom comparator, you also need to pass a value for the `reverse`
 * argument - passing `false` retains the default sorting order, i.e. ascending.)
 *
   <example name="orderBy-custom-comparator" module="orderByExample4">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <div class="friends-container custom-comparator">
           <h3>Locale-sensitive Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter':false:localeSensitiveComparator">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
         <div class="friends-container default-comparator">
           <h3>Default Comparator</h3>
           <table class="friends">
             <tr>
               <th>Name</th>
               <th>Favorite Letter</th>
             </tr>
             <tr ng-repeat="friend in friends | orderBy:'favoriteLetter'">
               <td>{{friend.name}}</td>
               <td>{{friend.favoriteLetter}}</td>
             </tr>
           </table>
         </div>
       </div>
     </file>
     <file name="script.js">
       angular.module('orderByExample4', [])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.friends = [
             {name: 'John',   favoriteLetter: 'Ä'},
             {name: 'Mary',   favoriteLetter: 'Ü'},
             {name: 'Mike',   favoriteLetter: 'Ö'},
             {name: 'Adam',   favoriteLetter: 'H'},
             {name: 'Julie',  favoriteLetter: 'Z'}
           ];

           $scope.localeSensitiveComparator = function(v1, v2) {
             // If we don't get strings, just compare by index
             if (v1.type !== 'string' || v2.type !== 'string') {
               return (v1.index < v2.index) ? -1 : 1;
             }

             // Compare strings alphabetically, taking locale into account
             return v1.value.localeCompare(v2.value);
           };
         }]);
     </file>
     <file name="style.css">
       .friends-container {
         display: inline-block;
         margin: 0 30px;
       }

       .friends {
         border-collapse: collapse;
       }

       .friends th {
         border-bottom: 1px solid;
       }
       .friends td, .friends th {
         border-left: 1px solid;
         padding: 5px 10px;
       }
       .friends td:first-child, .friends th:first-child {
         border-left: none;
       }
     </file>
     <file name="protractor.js" type="protractor">
       // Element locators
       var container = element(by.css('.custom-comparator'));
       var names = container.all(by.repeater('friends').column('friend.name'));

       it('should sort friends by favorite letter (in correct alphabetical order)', function() {
         expect(names.get(0).getText()).toBe('John');
         expect(names.get(1).getText()).toBe('Adam');
         expect(names.get(2).getText()).toBe('Mike');
         expect(names.get(3).getText()).toBe('Mary');
         expect(names.get(4).getText()).toBe('Julie');
       });
     </file>
   </example>
 *
 */
orderByFilter.$inject = ['$parse'];
function orderByFilter($parse) {
  return function(array, sortPredicate, reverseOrder, compareFn) {

    if (array == null) return array;
    if (!isArrayLike(array)) {
      throw minErr('orderBy')('notarray', 'Expected array but received: {0}', array);
    }

    if (!isArray(sortPredicate)) { sortPredicate = [sortPredicate]; }
    if (sortPredicate.length === 0) { sortPredicate = ['+']; }

    var predicates = processPredicates(sortPredicate);

    var descending = reverseOrder ? -1 : 1;

    // Define the `compare()` function. Use a default comparator if none is specified.
    var compare = isFunction(compareFn) ? compareFn : defaultCompare;

    // The next three lines are a version of a Swartzian Transform idiom from Perl
    // (sometimes called the Decorate-Sort-Undecorate idiom)
    // See https://en.wikipedia.org/wiki/Schwartzian_transform
    var compareValues = Array.prototype.map.call(array, getComparisonObject);
    compareValues.sort(doComparison);
    array = compareValues.map(function(item) { return item.value; });

    return array;

    function getComparisonObject(value, index) {
      // NOTE: We are adding an extra `tieBreaker` value based on the element's index.
      // This will be used to keep the sort stable when none of the input predicates can
      // distinguish between two elements.
      return {
        value: value,
        tieBreaker: {value: index, type: 'number', index: index},
        predicateValues: predicates.map(function(predicate) {
          return getPredicateValue(predicate.get(value), index);
        })
      };
    }

    function doComparison(v1, v2) {
      for (var i = 0, ii = predicates.length; i < ii; i++) {
        var result = compare(v1.predicateValues[i], v2.predicateValues[i]);
        if (result) {
          return result * predicates[i].descending * descending;
        }
      }

      return (compare(v1.tieBreaker, v2.tieBreaker) || defaultCompare(v1.tieBreaker, v2.tieBreaker)) * descending;
    }
  };

  function processPredicates(sortPredicates) {
    return sortPredicates.map(function(predicate) {
      var descending = 1, get = identity;

      if (isFunction(predicate)) {
        get = predicate;
      } else if (isString(predicate)) {
        if ((predicate.charAt(0) === '+' || predicate.charAt(0) === '-')) {
          descending = predicate.charAt(0) === '-' ? -1 : 1;
          predicate = predicate.substring(1);
        }
        if (predicate !== '') {
          get = $parse(predicate);
          if (get.constant) {
            var key = get();
            get = function(value) { return value[key]; };
          }
        }
      }
      return {get: get, descending: descending};
    });
  }

  function isPrimitive(value) {
    switch (typeof value) {
      case 'number': /* falls through */
      case 'boolean': /* falls through */
      case 'string':
        return true;
      default:
        return false;
    }
  }

  function objectValue(value) {
    // If `valueOf` is a valid function use that
    if (isFunction(value.valueOf)) {
      value = value.valueOf();
      if (isPrimitive(value)) return value;
    }
    // If `toString` is a valid function and not the one from `Object.prototype` use that
    if (hasCustomToString(value)) {
      value = value.toString();
      if (isPrimitive(value)) return value;
    }

    return value;
  }

  function getPredicateValue(value, index) {
    var type = typeof value;
    if (value === null) {
      type = 'string';
      value = 'null';
    } else if (type === 'object') {
      value = objectValue(value);
    }
    return {value: value, type: type, index: index};
  }

  function defaultCompare(v1, v2) {
    var result = 0;
    var type1 = v1.type;
    var type2 = v2.type;

    if (type1 === type2) {
      var value1 = v1.value;
      var value2 = v2.value;

      if (type1 === 'string') {
        // Compare strings case-insensitively
        value1 = value1.toLowerCase();
        value2 = value2.toLowerCase();
      } else if (type1 === 'object') {
        // For basic objects, use the position of the object
        // in the collection instead of the value
        if (isObject(value1)) value1 = v1.index;
        if (isObject(value2)) value2 = v2.index;
      }

      if (value1 !== value2) {
        result = value1 < value2 ? -1 : 1;
      }
    } else {
      result = type1 < type2 ? -1 : 1;
    }

    return result;
  }
}

function ngDirective(directive) {
  if (isFunction(directive)) {
    directive = {
      link: directive
    };
  }
  directive.restrict = directive.restrict || 'AC';
  return valueFn(directive);
}

/**
 * @ngdoc directive
 * @name a
 * @restrict E
 *
 * @description
 * Modifies the default behavior of the html a tag so that the default action is prevented when
 * the href attribute is empty.
 *
 * For dynamically creating `href` attributes for a tags, see the {@link ng.ngHref `ngHref`} directive.
 */
var htmlAnchorDirective = valueFn({
  restrict: 'E',
  compile: function(element, attr) {
    if (!attr.href && !attr.xlinkHref) {
      return function(scope, element) {
        // If the linked element is not an anchor tag anymore, do nothing
        if (element[0].nodeName.toLowerCase() !== 'a') return;

        // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
        var href = toString.call(element.prop('href')) === '[object SVGAnimatedString]' ?
                   'xlink:href' : 'href';
        element.on('click', function(event) {
          // if we have no href url, then don't navigate anywhere.
          if (!element.attr(href)) {
            event.preventDefault();
          }
        });
      };
    }
  }
});

/**
 * @ngdoc directive
 * @name ngHref
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in an href attribute will
 * make the link go to the wrong URL if the user clicks it before
 * Angular has a chance to replace the `{{hash}}` markup with its
 * value. Until Angular replaces the markup the link will be broken
 * and will most likely return a 404 error. The `ngHref` directive
 * solves this problem.
 *
 * The wrong way to write it:
 * ```html
 * <a href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <a ng-href="http://www.gravatar.com/avatar/{{hash}}">link1</a>
 * ```
 *
 * @element A
 * @param {template} ngHref any string which can contain `{{}}` markup.
 *
 * @example
 * This example shows various combinations of `href`, `ng-href` and `ng-click` attributes
 * in links and their different behaviors:
    <example name="ng-href">
      <file name="index.html">
        <input ng-model="value" /><br />
        <a id="link-1" href ng-click="value = 1">link 1</a> (link, don't reload)<br />
        <a id="link-2" href="" ng-click="value = 2">link 2</a> (link, don't reload)<br />
        <a id="link-3" ng-href="/{{'123'}}">link 3</a> (link, reload!)<br />
        <a id="link-4" href="" name="xx" ng-click="value = 4">anchor</a> (link, don't reload)<br />
        <a id="link-5" name="xxx" ng-click="value = 5">anchor</a> (no link)<br />
        <a id="link-6" ng-href="{{value}}">link</a> (link, change location)
      </file>
      <file name="protractor.js" type="protractor">
        it('should execute ng-click but not reload when href without value', function() {
          element(by.id('link-1')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('1');
          expect(element(by.id('link-1')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when href empty string', function() {
          element(by.id('link-2')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('2');
          expect(element(by.id('link-2')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click and change url when ng-href specified', function() {
          expect(element(by.id('link-3')).getAttribute('href')).toMatch(/\/123$/);

          element(by.id('link-3')).click();

          // At this point, we navigate away from an Angular page, so we need
          // to use browser.driver to get the base webdriver.

          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/123$/);
            });
          }, 5000, 'page should navigate to /123');
        });

        it('should execute ng-click but not reload when href empty string and name specified', function() {
          element(by.id('link-4')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('4');
          expect(element(by.id('link-4')).getAttribute('href')).toBe('');
        });

        it('should execute ng-click but not reload when no href but name specified', function() {
          element(by.id('link-5')).click();
          expect(element(by.model('value')).getAttribute('value')).toEqual('5');
          expect(element(by.id('link-5')).getAttribute('href')).toBe(null);
        });

        it('should only change url when only ng-href', function() {
          element(by.model('value')).clear();
          element(by.model('value')).sendKeys('6');
          expect(element(by.id('link-6')).getAttribute('href')).toMatch(/\/6$/);

          element(by.id('link-6')).click();

          // At this point, we navigate away from an Angular page, so we need
          // to use browser.driver to get the base webdriver.
          browser.wait(function() {
            return browser.driver.getCurrentUrl().then(function(url) {
              return url.match(/\/6$/);
            });
          }, 5000, 'page should navigate to /6');
        });
      </file>
    </example>
 */

/**
 * @ngdoc directive
 * @name ngSrc
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in a `src` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until Angular replaces the expression inside
 * `{{hash}}`. The `ngSrc` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img src="http://www.gravatar.com/avatar/{{hash}}" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-src="http://www.gravatar.com/avatar/{{hash}}" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrc any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngSrcset
 * @restrict A
 * @priority 99
 *
 * @description
 * Using Angular markup like `{{hash}}` in a `srcset` attribute doesn't
 * work right: The browser will fetch from the URL with the literal
 * text `{{hash}}` until Angular replaces the expression inside
 * `{{hash}}`. The `ngSrcset` directive solves this problem.
 *
 * The buggy way to write it:
 * ```html
 * <img srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description"/>
 * ```
 *
 * The correct way to write it:
 * ```html
 * <img ng-srcset="http://www.gravatar.com/avatar/{{hash}} 2x" alt="Description" />
 * ```
 *
 * @element IMG
 * @param {template} ngSrcset any string which can contain `{{}}` markup.
 */

/**
 * @ngdoc directive
 * @name ngDisabled
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * This directive sets the `disabled` attribute on the element (typically a form control,
 * e.g. `input`, `button`, `select` etc.) if the
 * {@link guide/expression expression} inside `ngDisabled` evaluates to truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `disabled`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-disabled">
      <file name="index.html">
        <label>Click me to toggle: <input type="checkbox" ng-model="checked"></label><br/>
        <button ng-model="button" ng-disabled="checked">Button</button>
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle button', function() {
          expect(element(by.css('button')).getAttribute('disabled')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('button')).getAttribute('disabled')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngDisabled If the {@link guide/expression expression} is truthy,
 *     then the `disabled` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngChecked
 * @restrict A
 * @priority 100
 *
 * @description
 * Sets the `checked` attribute on the element, if the expression inside `ngChecked` is truthy.
 *
 * Note that this directive should not be used together with {@link ngModel `ngModel`},
 * as this can lead to unexpected behavior.
 *
 * A special directive is necessary because we cannot use interpolation inside the `checked`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-checked">
      <file name="index.html">
        <label>Check me to check both: <input type="checkbox" ng-model="master"></label><br/>
        <input id="checkSlave" type="checkbox" ng-checked="master" aria-label="Slave input">
      </file>
      <file name="protractor.js" type="protractor">
        it('should check both checkBoxes', function() {
          expect(element(by.id('checkSlave')).getAttribute('checked')).toBeFalsy();
          element(by.model('master')).click();
          expect(element(by.id('checkSlave')).getAttribute('checked')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngChecked If the {@link guide/expression expression} is truthy,
 *     then the `checked` attribute will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngReadonly
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `readonly` attribute on the element, if the expression inside `ngReadonly` is truthy.
 * Note that `readonly` applies only to `input` elements with specific types. [See the input docs on
 * MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly) for more information.
 *
 * A special directive is necessary because we cannot use interpolation inside the `readonly`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * @example
    <example name="ng-readonly">
      <file name="index.html">
        <label>Check me to make text readonly: <input type="checkbox" ng-model="checked"></label><br/>
        <input type="text" ng-readonly="checked" value="I'm Angular" aria-label="Readonly field" />
      </file>
      <file name="protractor.js" type="protractor">
        it('should toggle readonly attr', function() {
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeFalsy();
          element(by.model('checked')).click();
          expect(element(by.css('[type="text"]')).getAttribute('readonly')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element INPUT
 * @param {expression} ngReadonly If the {@link guide/expression expression} is truthy,
 *     then special attribute "readonly" will be set on the element
 */


/**
 * @ngdoc directive
 * @name ngSelected
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `selected` attribute on the element, if the expression inside `ngSelected` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `selected`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * <div class="alert alert-warning">
 *   **Note:** `ngSelected` does not interact with the `select` and `ngModel` directives, it only
 *   sets the `selected` attribute on the element. If you are using `ngModel` on the select, you
 *   should not use `ngSelected` on the options, as `ngModel` will set the select value and
 *   selected options.
 * </div>
 *
 * @example
    <example name="ng-selected">
      <file name="index.html">
        <label>Check me to select: <input type="checkbox" ng-model="selected"></label><br/>
        <select aria-label="ngSelected demo">
          <option>Hello!</option>
          <option id="greet" ng-selected="selected">Greetings!</option>
        </select>
      </file>
      <file name="protractor.js" type="protractor">
        it('should select Greetings!', function() {
          expect(element(by.id('greet')).getAttribute('selected')).toBeFalsy();
          element(by.model('selected')).click();
          expect(element(by.id('greet')).getAttribute('selected')).toBeTruthy();
        });
      </file>
    </example>
 *
 * @element OPTION
 * @param {expression} ngSelected If the {@link guide/expression expression} is truthy,
 *     then special attribute "selected" will be set on the element
 */

/**
 * @ngdoc directive
 * @name ngOpen
 * @restrict A
 * @priority 100
 *
 * @description
 *
 * Sets the `open` attribute on the element, if the expression inside `ngOpen` is truthy.
 *
 * A special directive is necessary because we cannot use interpolation inside the `open`
 * attribute. See the {@link guide/interpolation interpolation guide} for more info.
 *
 * ## A note about browser compatibility
 *
 * Internet Explorer and Edge do not support the `details` element, it is
 * recommended to use {@link ng.ngShow} and {@link ng.ngHide} instead.
 *
 * @example
     <example name="ng-open">
       <file name="index.html">
         <label>Toggle details: <input type="checkbox" ng-model="open"></label><br/>
         <details id="details" ng-open="open">
            <summary>List</summary>
            <ul>
              <li>Apple</li>
              <li>Orange</li>
              <li>Durian</li>
            </ul>
         </details>
       </file>
       <file name="protractor.js" type="protractor">
         it('should toggle open', function() {
           expect(element(by.id('details')).getAttribute('open')).toBeFalsy();
           element(by.model('open')).click();
           expect(element(by.id('details')).getAttribute('open')).toBeTruthy();
         });
       </file>
     </example>
 *
 * @element DETAILS
 * @param {expression} ngOpen If the {@link guide/expression expression} is truthy,
 *     then special attribute "open" will be set on the element
 */

var ngAttributeAliasDirectives = {};

// boolean attrs are evaluated
forEach(BOOLEAN_ATTR, function(propName, attrName) {
  // binding to multiple is not supported
  if (propName === 'multiple') return;

  function defaultLinkFn(scope, element, attr) {
    scope.$watch(attr[normalized], function ngBooleanAttrWatchAction(value) {
      attr.$set(attrName, !!value);
    });
  }

  var normalized = directiveNormalize('ng-' + attrName);
  var linkFn = defaultLinkFn;

  if (propName === 'checked') {
    linkFn = function(scope, element, attr) {
      // ensuring ngChecked doesn't interfere with ngModel when both are set on the same input
      if (attr.ngModel !== attr[normalized]) {
        defaultLinkFn(scope, element, attr);
      }
    };
  }

  ngAttributeAliasDirectives[normalized] = function() {
    return {
      restrict: 'A',
      priority: 100,
      link: linkFn
    };
  };
});

// aliased input attrs are evaluated
forEach(ALIASED_ATTR, function(htmlAttr, ngAttr) {
  ngAttributeAliasDirectives[ngAttr] = function() {
    return {
      priority: 100,
      link: function(scope, element, attr) {
        //special case ngPattern when a literal regular expression value
        //is used as the expression (this way we don't have to watch anything).
        if (ngAttr === 'ngPattern' && attr.ngPattern.charAt(0) === '/') {
          var match = attr.ngPattern.match(REGEX_STRING_REGEXP);
          if (match) {
            attr.$set('ngPattern', new RegExp(match[1], match[2]));
            return;
          }
        }

        scope.$watch(attr[ngAttr], function ngAttrAliasWatchAction(value) {
          attr.$set(ngAttr, value);
        });
      }
    };
  };
});

// ng-src, ng-srcset, ng-href are interpolated
forEach(['src', 'srcset', 'href'], function(attrName) {
  var normalized = directiveNormalize('ng-' + attrName);
  ngAttributeAliasDirectives[normalized] = function() {
    return {
      priority: 99, // it needs to run after the attributes are interpolated
      link: function(scope, element, attr) {
        var propName = attrName,
            name = attrName;

        if (attrName === 'href' &&
            toString.call(element.prop('href')) === '[object SVGAnimatedString]') {
          name = 'xlinkHref';
          attr.$attr[name] = 'xlink:href';
          propName = null;
        }

        attr.$observe(normalized, function(value) {
          if (!value) {
            if (attrName === 'href') {
              attr.$set(name, null);
            }
            return;
          }

          attr.$set(name, value);

          // Support: IE 9-11 only
          // On IE, if "ng:src" directive declaration is used and "src" attribute doesn't exist
          // then calling element.setAttribute('src', 'foo') doesn't do anything, so we need
          // to set the property as well to achieve the desired effect.
          // We use attr[attrName] value since $set can sanitize the url.
          if (msie && propName) element.prop(propName, attr[name]);
        });
      }
    };
  };
});

/* global -nullFormCtrl, -PENDING_CLASS, -SUBMITTED_CLASS
 */
var nullFormCtrl = {
  $addControl: noop,
  $$renameControl: nullFormRenameControl,
  $removeControl: noop,
  $setValidity: noop,
  $setDirty: noop,
  $setPristine: noop,
  $setSubmitted: noop
},
PENDING_CLASS = 'ng-pending',
SUBMITTED_CLASS = 'ng-submitted';

function nullFormRenameControl(control, name) {
  control.$name = name;
}

/**
 * @ngdoc type
 * @name form.FormController
 *
 * @property {boolean} $pristine True if user has not interacted with the form yet.
 * @property {boolean} $dirty True if user has already interacted with the form.
 * @property {boolean} $valid True if all of the containing forms and controls are valid.
 * @property {boolean} $invalid True if at least one containing control or form is invalid.
 * @property {boolean} $submitted True if user has submitted the form even if its invalid.
 *
 * @property {Object} $pending An object hash, containing references to controls or forms with
 *  pending validators, where:
 *
 *  - keys are validations tokens (error names).
 *  - values are arrays of controls or forms that have a pending validator for the given error name.
 *
 * See {@link form.FormController#$error $error} for a list of built-in validation tokens.
 *
 * @property {Object} $error An object hash, containing references to controls or forms with failing
 *  validators, where:
 *
 *  - keys are validation tokens (error names),
 *  - values are arrays of controls or forms that have a failing validator for the given error name.
 *
 *  Built-in validation tokens:
 *  - `email`
 *  - `max`
 *  - `maxlength`
 *  - `min`
 *  - `minlength`
 *  - `number`
 *  - `pattern`
 *  - `required`
 *  - `url`
 *  - `date`
 *  - `datetimelocal`
 *  - `time`
 *  - `week`
 *  - `month`
 *
 * @description
 * `FormController` keeps track of all its controls and nested forms as well as the state of them,
 * such as being valid/invalid or dirty/pristine.
 *
 * Each {@link ng.directive:form form} directive creates an instance
 * of `FormController`.
 *
 */
//asks for $scope to fool the BC controller module
FormController.$inject = ['$element', '$attrs', '$scope', '$animate', '$interpolate'];
function FormController($element, $attrs, $scope, $animate, $interpolate) {
  this.$$controls = [];

  // init state
  this.$error = {};
  this.$$success = {};
  this.$pending = undefined;
  this.$name = $interpolate($attrs.name || $attrs.ngForm || '')($scope);
  this.$dirty = false;
  this.$pristine = true;
  this.$valid = true;
  this.$invalid = false;
  this.$submitted = false;
  this.$$parentForm = nullFormCtrl;

  this.$$element = $element;
  this.$$animate = $animate;

  setupValidity(this);
}

FormController.prototype = {
  /**
   * @ngdoc method
   * @name form.FormController#$rollbackViewValue
   *
   * @description
   * Rollback all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is typically needed by the reset button of
   * a form that uses `ng-model-options` to pend updates.
   */
  $rollbackViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$rollbackViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$commitViewValue
   *
   * @description
   * Commit all form controls pending updates to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. This method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    forEach(this.$$controls, function(control) {
      control.$commitViewValue();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$addControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Register a control with the form. Input elements using ngModelController do this automatically
   * when they are linked.
   *
   * Note that the current state of the control will not be reflected on the new parent form. This
   * is not an issue with normal use, as freshly compiled and linked controls are in a `$pristine`
   * state.
   *
   * However, if the method is used programmatically, for example by adding dynamically created controls,
   * or controls that have been previously removed without destroying their corresponding DOM element,
   * it's the developers responsibility to make sure the current state propagates to the parent form.
   *
   * For example, if an input control is added that is already `$dirty` and has `$error` properties,
   * calling `$setDirty()` and `$validate()` afterwards will propagate the state to the parent form.
   */
  $addControl: function(control) {
    // Breaking change - before, inputs whose name was "hasOwnProperty" were quietly ignored
    // and not added to the scope.  Now we throw an error.
    assertNotHasOwnProperty(control.$name, 'input');
    this.$$controls.push(control);

    if (control.$name) {
      this[control.$name] = control;
    }

    control.$$parentForm = this;
  },

  // Private API: rename a form control
  $$renameControl: function(control, newName) {
    var oldName = control.$name;

    if (this[oldName] === control) {
      delete this[oldName];
    }
    this[newName] = control;
    control.$name = newName;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$removeControl
   * @param {object} control control object, either a {@link form.FormController} or an
   * {@link ngModel.NgModelController}
   *
   * @description
   * Deregister a control from the form.
   *
   * Input elements using ngModelController do this automatically when they are destroyed.
   *
   * Note that only the removed control's validation state (`$errors`etc.) will be removed from the
   * form. `$dirty`, `$submitted` states will not be changed, because the expected behavior can be
   * different from case to case. For example, removing the only `$dirty` control from a form may or
   * may not mean that the form is still `$dirty`.
   */
  $removeControl: function(control) {
    if (control.$name && this[control.$name] === control) {
      delete this[control.$name];
    }
    forEach(this.$pending, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$error, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);
    forEach(this.$$success, function(value, name) {
      // eslint-disable-next-line no-invalid-this
      this.$setValidity(name, null, control);
    }, this);

    arrayRemove(this.$$controls, control);
    control.$$parentForm = nullFormCtrl;
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setDirty
   *
   * @description
   * Sets the form to a dirty state.
   *
   * This method can be called to add the 'ng-dirty' class and set the form to a dirty
   * state (ng-dirty class). This method will also propagate to parent forms.
   */
  $setDirty: function() {
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$dirty = true;
    this.$pristine = false;
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setPristine
   *
   * @description
   * Sets the form to its pristine state.
   *
   * This method sets the form's `$pristine` state to true, the `$dirty` state to false, removes
   * the `ng-dirty` class and adds the `ng-pristine` class. Additionally, it sets the `$submitted`
   * state to false.
   *
   * This method will also propagate to all the controls contained in this form.
   *
   * Setting a form back to a pristine state is often useful when we want to 'reuse' a form after
   * saving or resetting it.
   */
  $setPristine: function() {
    this.$$animate.setClass(this.$$element, PRISTINE_CLASS, DIRTY_CLASS + ' ' + SUBMITTED_CLASS);
    this.$dirty = false;
    this.$pristine = true;
    this.$submitted = false;
    forEach(this.$$controls, function(control) {
      control.$setPristine();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setUntouched
   *
   * @description
   * Sets the form to its untouched state.
   *
   * This method can be called to remove the 'ng-touched' class and set the form controls to their
   * untouched state (ng-untouched class).
   *
   * Setting a form controls back to their untouched state is often useful when setting the form
   * back to its pristine state.
   */
  $setUntouched: function() {
    forEach(this.$$controls, function(control) {
      control.$setUntouched();
    });
  },

  /**
   * @ngdoc method
   * @name form.FormController#$setSubmitted
   *
   * @description
   * Sets the form to its submitted state.
   */
  $setSubmitted: function() {
    this.$$animate.addClass(this.$$element, SUBMITTED_CLASS);
    this.$submitted = true;
    this.$$parentForm.$setSubmitted();
  }
};

/**
 * @ngdoc method
 * @name form.FormController#$setValidity
 *
 * @description
 * Change the validity state of the form, and notify the parent form (if any).
 *
 * Application developers will rarely need to call this method directly. It is used internally, by
 * {@link ngModel.NgModelController#$setValidity NgModelController.$setValidity()}, to propagate a
 * control's validity state to the parent `FormController`.
 *
 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be
 *        assigned to either `$error[validationErrorKey]` or `$pending[validationErrorKey]` (for
 *        unfulfilled `$asyncValidators`), so that it is available for data-binding. The
 *        `validationErrorKey` should be in camelCase and will get converted into dash-case for
 *        class name. Example: `myError` will result in `ng-valid-my-error` and
 *        `ng-invalid-my-error` classes and can be bound to as `{{ someForm.$error.myError }}`.
 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending
 *        (undefined),  or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
 *        Skipped is used by AngularJS when validators do not run because of parse errors and when
 *        `$asyncValidators` do not run because any of the `$validators` failed.
 * @param {NgModelController | FormController} controller - The controller whose validity state is
 *        triggering the change.
 */
addSetValidityMethod({
  clazz: FormController,
  set: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      object[property] = [controller];
    } else {
      var index = list.indexOf(controller);
      if (index === -1) {
        list.push(controller);
      }
    }
  },
  unset: function(object, property, controller) {
    var list = object[property];
    if (!list) {
      return;
    }
    arrayRemove(list, controller);
    if (list.length === 0) {
      delete object[property];
    }
  }
});

/**
 * @ngdoc directive
 * @name ngForm
 * @restrict EAC
 *
 * @description
 * Nestable alias of {@link ng.directive:form `form`} directive. HTML
 * does not allow nesting of form elements. It is useful to nest forms, for example if the validity of a
 * sub-group of controls needs to be determined.
 *
 * Note: the purpose of `ngForm` is to group controls,
 * but not to be a replacement for the `<form>` tag with all of its capabilities
 * (e.g. posting to the server, ...).
 *
 * @param {string=} ngForm|name Name of the form. If specified, the form controller will be published into
 *                       related scope, under this name.
 *
 */

 /**
 * @ngdoc directive
 * @name form
 * @restrict E
 *
 * @description
 * Directive that instantiates
 * {@link form.FormController FormController}.
 *
 * If the `name` attribute is specified, the form controller is published onto the current scope under
 * this name.
 *
 * # Alias: {@link ng.directive:ngForm `ngForm`}
 *
 * In Angular, forms can be nested. This means that the outer form is valid when all of the child
 * forms are valid as well. However, browsers do not allow nesting of `<form>` elements, so
 * Angular provides the {@link ng.directive:ngForm `ngForm`} directive, which behaves identically to
 * `form` but can be nested. Nested forms can be useful, for example, if the validity of a sub-group
 * of controls needs to be determined.
 *
 * # CSS classes
 *  - `ng-valid` is set if the form is valid.
 *  - `ng-invalid` is set if the form is invalid.
 *  - `ng-pending` is set if the form is pending.
 *  - `ng-pristine` is set if the form is pristine.
 *  - `ng-dirty` is set if the form is dirty.
 *  - `ng-submitted` is set if the form was submitted.
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 *
 * # Submitting a form and preventing the default action
 *
 * Since the role of forms in client-side Angular applications is different than in classical
 * roundtrip apps, it is desirable for the browser not to translate the form submission into a full
 * page reload that sends the data to the server. Instead some javascript logic should be triggered
 * to handle the form submission in an application-specific way.
 *
 * For this reason, Angular prevents the default action (form submission to the server) unless the
 * `<form>` element has an `action` attribute specified.
 *
 * You can use one of the following two ways to specify what javascript method should be called when
 * a form is submitted:
 *
 * - {@link ng.directive:ngSubmit ngSubmit} directive on the form element
 * - {@link ng.directive:ngClick ngClick} directive on the first
  *  button or input field of type submit (input[type=submit])
 *
 * To prevent double execution of the handler, use only one of the {@link ng.directive:ngSubmit ngSubmit}
 * or {@link ng.directive:ngClick ngClick} directives.
 * This is because of the following form submission rules in the HTML specification:
 *
 * - If a form has only one input field then hitting enter in this field triggers form submit
 * (`ngSubmit`)
 * - if a form has 2+ input fields and no buttons or input[type=submit] then hitting enter
 * doesn't trigger submit
 * - if a form has one or more input fields and one or more buttons or input[type=submit] then
 * hitting enter in any of the input fields will trigger the click handler on the *first* button or
 * input[type=submit] (`ngClick`) *and* a submit handler on the enclosing form (`ngSubmit`)
 *
 * Any pending `ngModelOptions` changes will take place immediately when an enclosing form is
 * submitted. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * ## Animation Hooks
 *
 * Animations in ngForm are triggered when any of the associated CSS classes are added and removed.
 * These classes are: `.ng-pristine`, `.ng-dirty`, `.ng-invalid` and `.ng-valid` as well as any
 * other validations that are performed within the form. Animations in ngForm are similar to how
 * they work in ngClass and animations can be hooked into using CSS transitions, keyframes as well
 * as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style a form element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-form {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-form.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
    <example name="ng-form" deps="angular-animate.js" animations="true" fixBase="true" module="formExample">
      <file name="index.html">
       <script>
         angular.module('formExample', [])
           .controller('FormController', ['$scope', function($scope) {
             $scope.userType = 'guest';
           }]);
       </script>
       <style>
        .my-form {
          transition:all linear 0.5s;
          background: transparent;
        }
        .my-form.ng-invalid {
          background: red;
        }
       </style>
       <form name="myForm" ng-controller="FormController" class="my-form">
         userType: <input name="input" ng-model="userType" required>
         <span class="error" ng-show="myForm.input.$error.required">Required!</span><br>
         <code>userType = {{userType}}</code><br>
         <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br>
         <code>myForm.input.$error = {{myForm.input.$error}}</code><br>
         <code>myForm.$valid = {{myForm.$valid}}</code><br>
         <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        it('should initialize to model', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));

          expect(userType.getText()).toContain('guest');
          expect(valid.getText()).toContain('true');
        });

        it('should be invalid if empty', function() {
          var userType = element(by.binding('userType'));
          var valid = element(by.binding('myForm.input.$valid'));
          var userInput = element(by.model('userType'));

          userInput.clear();
          userInput.sendKeys('');

          expect(userType.getText()).toEqual('userType =');
          expect(valid.getText()).toContain('false');
        });
      </file>
    </example>
 *
 * @param {string=} name Name of the form. If specified, the form controller will be published into
 *                       related scope, under this name.
 */
var formDirectiveFactory = function(isNgForm) {
  return ['$timeout', '$parse', function($timeout, $parse) {
    var formDirective = {
      name: 'form',
      restrict: isNgForm ? 'EAC' : 'E',
      require: ['form', '^^?form'], //first is the form's own ctrl, second is an optional parent form
      controller: FormController,
      compile: function ngFormCompile(formElement, attr) {
        // Setup initial state of the control
        formElement.addClass(PRISTINE_CLASS).addClass(VALID_CLASS);

        var nameAttr = attr.name ? 'name' : (isNgForm && attr.ngForm ? 'ngForm' : false);

        return {
          pre: function ngFormPreLink(scope, formElement, attr, ctrls) {
            var controller = ctrls[0];

            // if `action` attr is not present on the form, prevent the default action (submission)
            if (!('action' in attr)) {
              // we can't use jq events because if a form is destroyed during submission the default
              // action is not prevented. see #1238
              //
              // IE 9 is not affected because it doesn't fire a submit event and try to do a full
              // page reload if the form was destroyed by submission of the form via a click handler
              // on a button in the form. Looks like an IE9 specific bug.
              var handleFormSubmission = function(event) {
                scope.$apply(function() {
                  controller.$commitViewValue();
                  controller.$setSubmitted();
                });

                event.preventDefault();
              };

              formElement[0].addEventListener('submit', handleFormSubmission);

              // unregister the preventDefault listener so that we don't not leak memory but in a
              // way that will achieve the prevention of the default action.
              formElement.on('$destroy', function() {
                $timeout(function() {
                  formElement[0].removeEventListener('submit', handleFormSubmission);
                }, 0, false);
              });
            }

            var parentFormCtrl = ctrls[1] || controller.$$parentForm;
            parentFormCtrl.$addControl(controller);

            var setter = nameAttr ? getSetter(controller.$name) : noop;

            if (nameAttr) {
              setter(scope, controller);
              attr.$observe(nameAttr, function(newValue) {
                if (controller.$name === newValue) return;
                setter(scope, undefined);
                controller.$$parentForm.$$renameControl(controller, newValue);
                setter = getSetter(controller.$name);
                setter(scope, controller);
              });
            }
            formElement.on('$destroy', function() {
              controller.$$parentForm.$removeControl(controller);
              setter(scope, undefined);
              extend(controller, nullFormCtrl); //stop propagating child destruction handlers upwards
            });
          }
        };
      }
    };

    return formDirective;

    function getSetter(expression) {
      if (expression === '') {
        //create an assignable expression, so forms with an empty name can be renamed later
        return $parse('this[""]').assign;
      }
      return $parse(expression).assign || noop;
    }
  }];
};

var formDirective = formDirectiveFactory();
var ngFormDirective = formDirectiveFactory(true);



// helper methods
function setupValidity(instance) {
  instance.$$classCache = {};
  instance.$$classCache[INVALID_CLASS] = !(instance.$$classCache[VALID_CLASS] = instance.$$element.hasClass(VALID_CLASS));
}
function addSetValidityMethod(context) {
  var clazz = context.clazz,
      set = context.set,
      unset = context.unset;

  clazz.prototype.$setValidity = function(validationErrorKey, state, controller) {
    if (isUndefined(state)) {
      createAndSet(this, '$pending', validationErrorKey, controller);
    } else {
      unsetAndCleanup(this, '$pending', validationErrorKey, controller);
    }
    if (!isBoolean(state)) {
      unset(this.$error, validationErrorKey, controller);
      unset(this.$$success, validationErrorKey, controller);
    } else {
      if (state) {
        unset(this.$error, validationErrorKey, controller);
        set(this.$$success, validationErrorKey, controller);
      } else {
        set(this.$error, validationErrorKey, controller);
        unset(this.$$success, validationErrorKey, controller);
      }
    }
    if (this.$pending) {
      cachedToggleClass(this, PENDING_CLASS, true);
      this.$valid = this.$invalid = undefined;
      toggleValidationCss(this, '', null);
    } else {
      cachedToggleClass(this, PENDING_CLASS, false);
      this.$valid = isObjectEmpty(this.$error);
      this.$invalid = !this.$valid;
      toggleValidationCss(this, '', this.$valid);
    }

    // re-read the state as the set/unset methods could have
    // combined state in this.$error[validationError] (used for forms),
    // where setting/unsetting only increments/decrements the value,
    // and does not replace it.
    var combinedState;
    if (this.$pending && this.$pending[validationErrorKey]) {
      combinedState = undefined;
    } else if (this.$error[validationErrorKey]) {
      combinedState = false;
    } else if (this.$$success[validationErrorKey]) {
      combinedState = true;
    } else {
      combinedState = null;
    }

    toggleValidationCss(this, validationErrorKey, combinedState);
    this.$$parentForm.$setValidity(validationErrorKey, combinedState, this);
  };

  function createAndSet(ctrl, name, value, controller) {
    if (!ctrl[name]) {
      ctrl[name] = {};
    }
    set(ctrl[name], value, controller);
  }

  function unsetAndCleanup(ctrl, name, value, controller) {
    if (ctrl[name]) {
      unset(ctrl[name], value, controller);
    }
    if (isObjectEmpty(ctrl[name])) {
      ctrl[name] = undefined;
    }
  }

  function cachedToggleClass(ctrl, className, switchValue) {
    if (switchValue && !ctrl.$$classCache[className]) {
      ctrl.$$animate.addClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = true;
    } else if (!switchValue && ctrl.$$classCache[className]) {
      ctrl.$$animate.removeClass(ctrl.$$element, className);
      ctrl.$$classCache[className] = false;
    }
  }

  function toggleValidationCss(ctrl, validationErrorKey, isValid) {
    validationErrorKey = validationErrorKey ? '-' + snake_case(validationErrorKey, '-') : '';

    cachedToggleClass(ctrl, VALID_CLASS + validationErrorKey, isValid === true);
    cachedToggleClass(ctrl, INVALID_CLASS + validationErrorKey, isValid === false);
  }
}

function isObjectEmpty(obj) {
  if (obj) {
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }
  }
  return true;
}

/* global
  VALID_CLASS: false,
  INVALID_CLASS: false,
  PRISTINE_CLASS: false,
  DIRTY_CLASS: false,
  ngModelMinErr: false
*/

// Regex code was initially obtained from SO prior to modification: https://stackoverflow.com/questions/3143070/javascript-regex-iso-datetime#answer-3143231
var ISO_DATE_REGEXP = /^\d{4,}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+(?:[+-][0-2]\d:[0-5]\d|Z)$/;
// See valid URLs in RFC3987 (http://tools.ietf.org/html/rfc3987)
// Note: We are being more lenient, because browsers are too.
//   1. Scheme
//   2. Slashes
//   3. Username
//   4. Password
//   5. Hostname
//   6. Port
//   7. Path
//   8. Query
//   9. Fragment
//                 1111111111111111 222   333333    44444        55555555555555555555555     666     77777777     8888888     999
var URL_REGEXP = /^[a-z][a-z\d.+-]*:\/*(?:[^:@]+(?::[^@]+)?@)?(?:[^\s:/?#]+|\[[a-f\d:]+])(?::\d+)?(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/i;
// eslint-disable-next-line max-len
var EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
var NUMBER_REGEXP = /^\s*(-|\+)?(\d+|(\d*(\.\d*)))([eE][+-]?\d+)?\s*$/;
var DATE_REGEXP = /^(\d{4,})-(\d{2})-(\d{2})$/;
var DATETIMELOCAL_REGEXP = /^(\d{4,})-(\d\d)-(\d\d)T(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;
var WEEK_REGEXP = /^(\d{4,})-W(\d\d)$/;
var MONTH_REGEXP = /^(\d{4,})-(\d\d)$/;
var TIME_REGEXP = /^(\d\d):(\d\d)(?::(\d\d)(\.\d{1,3})?)?$/;

var PARTIAL_VALIDATION_EVENTS = 'keydown wheel mousedown';
var PARTIAL_VALIDATION_TYPES = createMap();
forEach('date,datetime-local,month,time,week'.split(','), function(type) {
  PARTIAL_VALIDATION_TYPES[type] = true;
});

var inputType = {

  /**
   * @ngdoc input
   * @name input[text]
   *
   * @description
   * Standard HTML text input with angular data binding, inherited by most of the `input` elements.
   *
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Adds `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
   *    This parameter is ignored for input[type=password] controls, which will never trim the
   *    input.
   *
   * @example
      <example name="text-input-directive" module="textInputExample">
        <file name="index.html">
         <script>
           angular.module('textInputExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 text: 'guest',
                 word: /^\s*\w*\s*$/
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Single word:
             <input type="text" name="input" ng-model="example.text"
                    ng-pattern="example.word" required ng-trim="false">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.pattern">
               Single word only!</span>
           </div>
           <code>text = {{example.text}}</code><br/>
           <code>myForm.input.$valid = {{myForm.input.$valid}}</code><br/>
           <code>myForm.input.$error = {{myForm.input.$error}}</code><br/>
           <code>myForm.$valid = {{myForm.$valid}}</code><br/>
           <code>myForm.$error.required = {{!!myForm.$error.required}}</code><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('example.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('guest');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if multi word', function() {
            input.clear();
            input.sendKeys('hello world');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'text': textInputType,

    /**
     * @ngdoc input
     * @name input[date]
     *
     * @description
     * Input with date validation and transformation. In browsers that do not yet support
     * the HTML5 date input, a text element will be used. In that case, text must be entered in a valid ISO-8601
     * date format (yyyy-MM-dd), for example: `2009-01-06`. Since many
     * modern browsers do not yet support this input type, it is important to provide cues to users on the
     * expected input format via a placeholder or label.
     *
     * The model must always be a Date object, otherwise Angular will throw an error.
     * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
     *
     * The timezone to be used to read/write the `Date` instance in the model can be defined using
     * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
     *
     * @param {string} ngModel Assignable angular expression to data-bind to.
     * @param {string=} name Property name of the form under which the control is published.
     * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`. This must be a
     *   valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `min="{{minDate | date:'yyyy-MM-dd'}}"`). Note that `min` will also add native HTML5
     *   constraint validation.
     * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`. This must be
     *   a valid ISO date string (yyyy-MM-dd). You can also use interpolation inside this attribute
     *   (e.g. `max="{{maxDate | date:'yyyy-MM-dd'}}"`). Note that `max` will also add native HTML5
     *   constraint validation.
     * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO date string
     *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
     * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO date string
     *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
     * @param {string=} required Sets `required` validation error key if the value is not entered.
     * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
     *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
     *    `required` when you want to data-bind to the `required` attribute.
     * @param {string=} ngChange Angular expression to be executed when input changes due to user
     *    interaction with the input element.
     *
     * @example
     <example name="date-input-directive" module="dateInputExample">
     <file name="index.html">
       <script>
          angular.module('dateInputExample', [])
            .controller('DateController', ['$scope', function($scope) {
              $scope.example = {
                value: new Date(2013, 9, 22)
              };
            }]);
       </script>
       <form name="myForm" ng-controller="DateController as dateCtrl">
          <label for="exampleInput">Pick a date in 2013:</label>
          <input type="date" id="exampleInput" name="input" ng-model="example.value"
              placeholder="yyyy-MM-dd" min="2013-01-01" max="2013-12-31" required />
          <div role="alert">
            <span class="error" ng-show="myForm.input.$error.required">
                Required!</span>
            <span class="error" ng-show="myForm.input.$error.date">
                Not a valid date!</span>
           </div>
           <tt>value = {{example.value | date: "yyyy-MM-dd"}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
       </form>
     </file>
     <file name="protractor.js" type="protractor">
        var value = element(by.binding('example.value | date: "yyyy-MM-dd"'));
        var valid = element(by.binding('myForm.input.$valid'));

        // currently protractor/webdriver does not support
        // sending keys to all known HTML5 input controls
        // for various browsers (see https://github.com/angular/protractor/issues/562).
        function setInput(val) {
          // set the value of the element and force validation.
          var scr = "var ipt = document.getElementById('exampleInput'); " +
          "ipt.value = '" + val + "';" +
          "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
          browser.executeScript(scr);
        }

        it('should initialize to model', function() {
          expect(value.getText()).toContain('2013-10-22');
          expect(valid.getText()).toContain('myForm.input.$valid = true');
        });

        it('should be invalid if empty', function() {
          setInput('');
          expect(value.getText()).toEqual('value =');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });

        it('should be invalid if over max', function() {
          setInput('2015-01-01');
          expect(value.getText()).toContain('');
          expect(valid.getText()).toContain('myForm.input.$valid = false');
        });
     </file>
     </example>
     */
  'date': createDateInputType('date', DATE_REGEXP,
         createDateParser(DATE_REGEXP, ['yyyy', 'MM', 'dd']),
         'yyyy-MM-dd'),

   /**
    * @ngdoc input
    * @name input[datetime-local]
    *
    * @description
    * Input with datetime validation and transformation. In browsers that do not yet support
    * the HTML5 date input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * local datetime format (yyyy-MM-ddTHH:mm:ss), for example: `2010-12-28T14:57:00`.
    *
    * The model must always be a Date object, otherwise Angular will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * @param {string} ngModel Assignable angular expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `min="{{minDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `min` will also add native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO datetime format (yyyy-MM-ddTHH:mm:ss). You can also use interpolation
    *   inside this attribute (e.g. `max="{{maxDatetimeLocal | date:'yyyy-MM-ddTHH:mm:ss'}}"`).
    *   Note that `max` will also add native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation error key to the Date / ISO datetime string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation error key to the Date / ISO datetime string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange Angular expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="datetimelocal-input-directive" module="dateExample">
    <file name="index.html">
      <script>
        angular.module('dateExample', [])
          .controller('DateController', ['$scope', function($scope) {
            $scope.example = {
              value: new Date(2010, 11, 28, 14, 57)
            };
          }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a date between in 2013:</label>
        <input type="datetime-local" id="exampleInput" name="input" ng-model="example.value"
            placeholder="yyyy-MM-ddTHH:mm:ss" min="2001-01-01T00:00:00" max="2013-12-31T00:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.datetimelocal">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-MM-ddTHH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM-ddTHH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2010-12-28T14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01-01T23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'datetime-local': createDateInputType('datetimelocal', DATETIMELOCAL_REGEXP,
      createDateParser(DATETIMELOCAL_REGEXP, ['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'sss']),
      'yyyy-MM-ddTHH:mm:ss.sss'),

  /**
   * @ngdoc input
   * @name input[time]
   *
   * @description
   * Input with time validation and transformation. In browsers that do not yet support
   * the HTML5 time input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * local time format (HH:mm:ss), for example: `14:57:00`. Model must be a Date object. This binding will always output a
   * Date object to the model of January 1, 1970, or local date `new Date(1970, 0, 1, HH, mm, ss)`.
   *
   * The model must always be a Date object, otherwise Angular will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minTime | date:'HH:mm:ss'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO time format (HH:mm:ss). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxTime | date:'HH:mm:ss'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO time string the
   *   `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO time string the
   *   `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="time-input-directive" module="timeExample">
   <file name="index.html">
     <script>
      angular.module('timeExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(1970, 0, 1, 14, 57, 0)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
        <label for="exampleInput">Pick a time between 8am and 5pm:</label>
        <input type="time" id="exampleInput" name="input" ng-model="example.value"
            placeholder="HH:mm:ss" min="08:00:00" max="17:00:00" required />
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.time">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "HH:mm:ss"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "HH:mm:ss"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('14:57:00');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('23:59:00');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'time': createDateInputType('time', TIME_REGEXP,
      createDateParser(TIME_REGEXP, ['HH', 'mm', 'ss', 'sss']),
     'HH:mm:ss.sss'),

   /**
    * @ngdoc input
    * @name input[week]
    *
    * @description
    * Input with week-of-the-year validation and transformation to Date. In browsers that do not yet support
    * the HTML5 week input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
    * week format (yyyy-W##), for example: `2013-W02`.
    *
    * The model must always be a Date object, otherwise Angular will throw an error.
    * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
    *
    * The timezone to be used to read/write the `Date` instance in the model can be defined using
    * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
    *
    * @param {string} ngModel Assignable angular expression to data-bind to.
    * @param {string=} name Property name of the form under which the control is published.
    * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `min="{{minWeek | date:'yyyy-Www'}}"`). Note that `min` will also add
    *   native HTML5 constraint validation.
    * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
    *   This must be a valid ISO week format (yyyy-W##). You can also use interpolation inside this
    *   attribute (e.g. `max="{{maxWeek | date:'yyyy-Www'}}"`). Note that `max` will also add
    *   native HTML5 constraint validation.
    * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
    *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
    * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
    *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.
    * @param {string=} required Sets `required` validation error key if the value is not entered.
    * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
    *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
    *    `required` when you want to data-bind to the `required` attribute.
    * @param {string=} ngChange Angular expression to be executed when input changes due to user
    *    interaction with the input element.
    *
    * @example
    <example name="week-input-directive" module="weekExample">
    <file name="index.html">
      <script>
      angular.module('weekExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 0, 3)
          };
        }]);
      </script>
      <form name="myForm" ng-controller="DateController as dateCtrl">
        <label>Pick a date between in 2013:
          <input id="exampleInput" type="week" name="input" ng-model="example.value"
                 placeholder="YYYY-W##" min="2012-W32"
                 max="2013-W52" required />
        </label>
        <div role="alert">
          <span class="error" ng-show="myForm.input.$error.required">
              Required!</span>
          <span class="error" ng-show="myForm.input.$error.week">
              Not a valid date!</span>
        </div>
        <tt>value = {{example.value | date: "yyyy-Www"}}</tt><br/>
        <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
        <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
        <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
        <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-Www"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-W01');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-W01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
    </file>
    </example>
    */
  'week': createDateInputType('week', WEEK_REGEXP, weekParser, 'yyyy-Www'),

  /**
   * @ngdoc input
   * @name input[month]
   *
   * @description
   * Input with month validation and transformation. In browsers that do not yet support
   * the HTML5 month input, a text element will be used. In that case, the text must be entered in a valid ISO-8601
   * month format (yyyy-MM), for example: `2009-01`.
   *
   * The model must always be a Date object, otherwise Angular will throw an error.
   * Invalid `Date` objects (dates whose `getTime()` is `NaN`) will be rendered as an empty string.
   * If the model is not set to the first of the month, the next view to model update will set it
   * to the first of the month.
   *
   * The timezone to be used to read/write the `Date` instance in the model can be defined using
   * {@link ng.directive:ngModelOptions ngModelOptions}. By default, this is the timezone of the browser.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `min="{{minMonth | date:'yyyy-MM'}}"`). Note that `min` will also add
   *   native HTML5 constraint validation.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *   This must be a valid ISO month format (yyyy-MM). You can also use interpolation inside this
   *   attribute (e.g. `max="{{maxMonth | date:'yyyy-MM'}}"`). Note that `max` will also add
   *   native HTML5 constraint validation.
   * @param {(date|string)=} ngMin Sets the `min` validation constraint to the Date / ISO week string
   *   the `ngMin` expression evaluates to. Note that it does not set the `min` attribute.
   * @param {(date|string)=} ngMax Sets the `max` validation constraint to the Date / ISO week string
   *   the `ngMax` expression evaluates to. Note that it does not set the `max` attribute.

   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
   <example name="month-input-directive" module="monthExample">
   <file name="index.html">
     <script>
      angular.module('monthExample', [])
        .controller('DateController', ['$scope', function($scope) {
          $scope.example = {
            value: new Date(2013, 9, 1)
          };
        }]);
     </script>
     <form name="myForm" ng-controller="DateController as dateCtrl">
       <label for="exampleInput">Pick a month in 2013:</label>
       <input id="exampleInput" type="month" name="input" ng-model="example.value"
          placeholder="yyyy-MM" min="2013-01" max="2013-12" required />
       <div role="alert">
         <span class="error" ng-show="myForm.input.$error.required">
            Required!</span>
         <span class="error" ng-show="myForm.input.$error.month">
            Not a valid month!</span>
       </div>
       <tt>value = {{example.value | date: "yyyy-MM"}}</tt><br/>
       <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
       <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
       <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
       <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
     </form>
   </file>
   <file name="protractor.js" type="protractor">
      var value = element(by.binding('example.value | date: "yyyy-MM"'));
      var valid = element(by.binding('myForm.input.$valid'));

      // currently protractor/webdriver does not support
      // sending keys to all known HTML5 input controls
      // for various browsers (https://github.com/angular/protractor/issues/562).
      function setInput(val) {
        // set the value of the element and force validation.
        var scr = "var ipt = document.getElementById('exampleInput'); " +
        "ipt.value = '" + val + "';" +
        "angular.element(ipt).scope().$apply(function(s) { s.myForm[ipt.name].$setViewValue('" + val + "'); });";
        browser.executeScript(scr);
      }

      it('should initialize to model', function() {
        expect(value.getText()).toContain('2013-10');
        expect(valid.getText()).toContain('myForm.input.$valid = true');
      });

      it('should be invalid if empty', function() {
        setInput('');
        expect(value.getText()).toEqual('value =');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });

      it('should be invalid if over max', function() {
        setInput('2015-01');
        expect(value.getText()).toContain('');
        expect(valid.getText()).toContain('myForm.input.$valid = false');
      });
   </file>
   </example>
   */
  'month': createDateInputType('month', MONTH_REGEXP,
     createDateParser(MONTH_REGEXP, ['yyyy', 'MM']),
     'yyyy-MM'),

  /**
   * @ngdoc input
   * @name input[number]
   *
   * @description
   * Text input with number validation and transformation. Sets the `number` validation
   * error if not a valid number.
   *
   * <div class="alert alert-warning">
   * The model must always be of type `number` otherwise Angular will throw an error.
   * Be aware that a string containing a number is not enough. See the {@link ngModel:numfmt}
   * error docs for more information and an example of how to convert your model if necessary.
   * </div>
   *
   * ## Issues with HTML5 constraint validation
   *
   * In browsers that follow the
   * [HTML5 specification](https://html.spec.whatwg.org/multipage/forms.html#number-state-%28type=number%29),
   * `input[number]` does not work as expected with {@link ngModelOptions `ngModelOptions.allowInvalid`}.
   * If a non-number is entered in the input, the browser will report the value as an empty string,
   * which means the view / model values in `ngModel` and subsequently the scope value
   * will also be an empty string.
   *
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation error key if the value entered is less than `min`.
   *    Can be interpolated.
   * @param {string=} max Sets the `max` validation error key if the value entered is greater than `max`.
   *    Can be interpolated.
   * @param {string=} ngMin Like `min`, sets the `min` validation error key if the value entered is less than `ngMin`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} ngMax Like `max`, sets the `max` validation error key if the value entered is greater than `ngMax`,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} step Sets the `step` validation error key if the value entered does not fit the `step` constraint.
   *    Can be interpolated.
   * @param {string=} ngStep Like `step`, sets the `step` validation error key if the value entered does not fit the `ngStep` constraint,
   *    but does not trigger HTML5 native validation. Takes an expression.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="number-input-directive" module="numberExample">
        <file name="index.html">
         <script>
           angular.module('numberExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.example = {
                 value: 12
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Number:
             <input type="number" name="input" ng-model="example.value"
                    min="0" max="99" required>
          </label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.number">
               Not valid number!</span>
           </div>
           <tt>value = {{example.value}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var value = element(by.binding('example.value'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('example.value'));

          it('should initialize to model', function() {
            expect(value.getText()).toContain('12');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if over max', function() {
            input.clear();
            input.sendKeys('123');
            expect(value.getText()).toEqual('value =');
            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'number': numberInputType,


  /**
   * @ngdoc input
   * @name input[url]
   *
   * @description
   * Text input with URL validation. Sets the `url` validation error key if the content is not a
   * valid URL.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[url]` uses a regex to validate urls that is derived from the regex
   * used in Chromium. If you need stricter validation, you can use `ng-pattern` or modify
   * the built-in validators (see the {@link guide/forms Forms guide})
   * </div>
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="url-input-directive" module="urlExample">
        <file name="index.html">
         <script>
           angular.module('urlExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.url = {
                 text: 'http://google.com'
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>URL:
             <input type="url" name="input" ng-model="url.text" required>
           <label>
           <div role="alert">
             <span class="error" ng-show="myForm.input.$error.required">
               Required!</span>
             <span class="error" ng-show="myForm.input.$error.url">
               Not valid url!</span>
           </div>
           <tt>text = {{url.text}}</tt><br/>
           <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
           <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
           <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
           <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
           <tt>myForm.$error.url = {{!!myForm.$error.url}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('url.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('url.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('http://google.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');

            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not url', function() {
            input.clear();
            input.sendKeys('box');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'url': urlInputType,


  /**
   * @ngdoc input
   * @name input[email]
   *
   * @description
   * Text input with email validation. Sets the `email` validation error key if not a valid email
   * address.
   *
   * <div class="alert alert-warning">
   * **Note:** `input[email]` uses a regex to validate email addresses that is derived from the regex
   * used in Chromium. If you need stricter validation (e.g. requiring a top-level domain), you can
   * use `ng-pattern` or modify the built-in validators (see the {@link guide/forms Forms guide})
   * </div>
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} required Sets `required` validation error key if the value is not entered.
   * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
   *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
   *    `required` when you want to data-bind to the `required` attribute.
   * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
   *    minlength.
   * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
   *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of
   *    any length.
   * @param {string=} pattern Similar to `ngPattern` except that the attribute value is the actual string
   *    that contains the regular expression body that will be converted to a regular expression
   *    as in the ngPattern directive.
   * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
   *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
   *    If the expression evaluates to a RegExp object, then this is used directly.
   *    If the expression evaluates to a string, then it will be converted to a RegExp
   *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
   *    `new RegExp('^abc$')`.<br />
   *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
   *    start at the index of the last search's match, thus not taking the whole input value into
   *    account.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="email-input-directive" module="emailExample">
        <file name="index.html">
         <script>
           angular.module('emailExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.email = {
                 text: 'me@example.com'
               };
             }]);
         </script>
           <form name="myForm" ng-controller="ExampleController">
             <label>Email:
               <input type="email" name="input" ng-model="email.text" required>
             </label>
             <div role="alert">
               <span class="error" ng-show="myForm.input.$error.required">
                 Required!</span>
               <span class="error" ng-show="myForm.input.$error.email">
                 Not valid email!</span>
             </div>
             <tt>text = {{email.text}}</tt><br/>
             <tt>myForm.input.$valid = {{myForm.input.$valid}}</tt><br/>
             <tt>myForm.input.$error = {{myForm.input.$error}}</tt><br/>
             <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
             <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
             <tt>myForm.$error.email = {{!!myForm.$error.email}}</tt><br/>
           </form>
         </file>
        <file name="protractor.js" type="protractor">
          var text = element(by.binding('email.text'));
          var valid = element(by.binding('myForm.input.$valid'));
          var input = element(by.model('email.text'));

          it('should initialize to model', function() {
            expect(text.getText()).toContain('me@example.com');
            expect(valid.getText()).toContain('true');
          });

          it('should be invalid if empty', function() {
            input.clear();
            input.sendKeys('');
            expect(text.getText()).toEqual('text =');
            expect(valid.getText()).toContain('false');
          });

          it('should be invalid if not email', function() {
            input.clear();
            input.sendKeys('xxx');

            expect(valid.getText()).toContain('false');
          });
        </file>
      </example>
   */
  'email': emailInputType,


  /**
   * @ngdoc input
   * @name input[radio]
   *
   * @description
   * HTML radio button.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string} value The value to which the `ngModel` expression should be set when selected.
   *    Note that `value` only supports `string` values, i.e. the scope model needs to be a string,
   *    too. Use `ngValue` if you need complex models (`number`, `object`, ...).
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   * @param {string} ngValue Angular expression to which `ngModel` will be be set when the radio
   *    is selected. Should be used instead of the `value` attribute if you need
   *    a non-string `ngModel` (`boolean`, `array`, ...).
   *
   * @example
      <example name="radio-input-directive" module="radioExample">
        <file name="index.html">
         <script>
           angular.module('radioExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.color = {
                 name: 'blue'
               };
               $scope.specialValue = {
                 "id": "12345",
                 "value": "green"
               };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>
             <input type="radio" ng-model="color.name" value="red">
             Red
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" ng-value="specialValue">
             Green
           </label><br/>
           <label>
             <input type="radio" ng-model="color.name" value="blue">
             Blue
           </label><br/>
           <tt>color = {{color.name | json}}</tt><br/>
          </form>
          Note that `ng-value="specialValue"` sets radio item's value to be the value of `$scope.specialValue`.
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var inputs = element.all(by.model('color.name'));
            var color = element(by.binding('color.name'));

            expect(color.getText()).toContain('blue');

            inputs.get(0).click();
            expect(color.getText()).toContain('red');

            inputs.get(1).click();
            expect(color.getText()).toContain('green');
          });
        </file>
      </example>
   */
  'radio': radioInputType,

  /**
   * @ngdoc input
   * @name input[range]
   *
   * @description
   * Native range input with validation and transformation.
   *
   * The model for the range input must always be a `Number`.
   *
   * IE9 and other browsers that do not support the `range` type fall back
   * to a text input without any default values for `min`, `max` and `step`. Model binding,
   * validation and number parsing are nevertheless supported.
   *
   * Browsers that support range (latest Chrome, Safari, Firefox, Edge) treat `input[range]`
   * in a way that never allows the input to hold an invalid value. That means:
   * - any non-numerical value is set to `(max + min) / 2`.
   * - any numerical value that is less than the current min val, or greater than the current max val
   * is set to the min / max val respectively.
   * - additionally, the current `step` is respected, so the nearest value that satisfies a step
   * is used.
   *
   * See the [HTML Spec on input[type=range]](https://www.w3.org/TR/html5/forms.html#range-state-(type=range))
   * for more info.
   *
   * This has the following consequences for Angular:
   *
   * Since the element value should always reflect the current model value, a range input
   * will set the bound ngModel expression to the value that the browser has set for the
   * input element. For example, in the following input `<input type="range" ng-model="model.value">`,
   * if the application sets `model.value = null`, the browser will set the input to `'50'`.
   * Angular will then set the model to `50`, to prevent input and model value being out of sync.
   *
   * That means the model for range will immediately be set to `50` after `ngModel` has been
   * initialized. It also means a range input can never have the required error.
   *
   * This does not only affect changes to the model value, but also to the values of the `min`,
   * `max`, and `step` attributes. When these change in a way that will cause the browser to modify
   * the input value, Angular will also update the model value.
   *
   * Automatic value adjustment also means that a range input element can never have the `required`,
   * `min`, or `max` errors.
   *
   * However, `step` is currently only fully implemented by Firefox. Other browsers have problems
   * when the step value changes dynamically - they do not adjust the element value correctly, but
   * instead may set the `stepMismatch` error. If that's the case, the Angular will set the `step`
   * error on the input, and set the model to `undefined`.
   *
   * Note that `input[range]` is not compatible with`ngMax`, `ngMin`, and `ngStep`, because they do
   * not set the `min` and `max` attributes, which means that the browser won't automatically adjust
   * the input value based on their values, and will always assume min = 0, max = 100, and step = 1.
   *
   * @param {string}  ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {string=} min Sets the `min` validation to ensure that the value entered is greater
   *                  than `min`. Can be interpolated.
   * @param {string=} max Sets the `max` validation to ensure that the value entered is less than `max`.
   *                  Can be interpolated.
   * @param {string=} step Sets the `step` validation to ensure that the value entered matches the `step`
   *                  Can be interpolated.
   * @param {string=} ngChange Angular expression to be executed when the ngModel value changes due
   *                  to user interaction with the input element.
   * @param {expression=} ngChecked If the expression is truthy, then the `checked` attribute will be set on the
   *                      element. **Note** : `ngChecked` should not be used alongside `ngModel`.
   *                      Checkout {@link ng.directive:ngChecked ngChecked} for usage.
   *
   * @example
      <example name="range-input-directive" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">

            Model as range: <input type="range" name="range" ng-model="value" min="{{min}}"  max="{{max}}">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   * ## Range Input with ngMin & ngMax attributes

   * @example
      <example name="range-input-directive-ng" module="rangeExample">
        <file name="index.html">
          <script>
            angular.module('rangeExample', [])
              .controller('ExampleController', ['$scope', function($scope) {
                $scope.value = 75;
                $scope.min = 10;
                $scope.max = 90;
              }]);
          </script>
          <form name="myForm" ng-controller="ExampleController">
            Model as range: <input type="range" name="range" ng-model="value" ng-min="min" ng-max="max">
            <hr>
            Model as number: <input type="number" ng-model="value"><br>
            Min: <input type="number" ng-model="min"><br>
            Max: <input type="number" ng-model="max"><br>
            value = <code>{{value}}</code><br/>
            myForm.range.$valid = <code>{{myForm.range.$valid}}</code><br/>
            myForm.range.$error = <code>{{myForm.range.$error}}</code>
          </form>
        </file>
      </example>

   */
  'range': rangeInputType,

  /**
   * @ngdoc input
   * @name input[checkbox]
   *
   * @description
   * HTML checkbox.
   *
   * @param {string} ngModel Assignable angular expression to data-bind to.
   * @param {string=} name Property name of the form under which the control is published.
   * @param {expression=} ngTrueValue The value to which the expression should be set when selected.
   * @param {expression=} ngFalseValue The value to which the expression should be set when not selected.
   * @param {string=} ngChange Angular expression to be executed when input changes due to user
   *    interaction with the input element.
   *
   * @example
      <example name="checkbox-input-directive" module="checkboxExample">
        <file name="index.html">
         <script>
           angular.module('checkboxExample', [])
             .controller('ExampleController', ['$scope', function($scope) {
               $scope.checkboxModel = {
                value1 : true,
                value2 : 'YES'
              };
             }]);
         </script>
         <form name="myForm" ng-controller="ExampleController">
           <label>Value1:
             <input type="checkbox" ng-model="checkboxModel.value1">
           </label><br/>
           <label>Value2:
             <input type="checkbox" ng-model="checkboxModel.value2"
                    ng-true-value="'YES'" ng-false-value="'NO'">
            </label><br/>
           <tt>value1 = {{checkboxModel.value1}}</tt><br/>
           <tt>value2 = {{checkboxModel.value2}}</tt><br/>
          </form>
        </file>
        <file name="protractor.js" type="protractor">
          it('should change state', function() {
            var value1 = element(by.binding('checkboxModel.value1'));
            var value2 = element(by.binding('checkboxModel.value2'));

            expect(value1.getText()).toContain('true');
            expect(value2.getText()).toContain('YES');

            element(by.model('checkboxModel.value1')).click();
            element(by.model('checkboxModel.value2')).click();

            expect(value1.getText()).toContain('false');
            expect(value2.getText()).toContain('NO');
          });
        </file>
      </example>
   */
  'checkbox': checkboxInputType,

  'hidden': noop,
  'button': noop,
  'submit': noop,
  'reset': noop,
  'file': noop
};

function stringBasedInputType(ctrl) {
  ctrl.$formatters.push(function(value) {
    return ctrl.$isEmpty(value) ? value : value.toString();
  });
}

function textInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);
}

function baseInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  var type = lowercase(element[0].type);

  // In composition mode, users are still inputting intermediate text buffer,
  // hold the listener until composition is done.
  // More about composition events: https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
  if (!$sniffer.android) {
    var composing = false;

    element.on('compositionstart', function() {
      composing = true;
    });

    element.on('compositionend', function() {
      composing = false;
      listener();
    });
  }

  var timeout;

  var listener = function(ev) {
    if (timeout) {
      $browser.defer.cancel(timeout);
      timeout = null;
    }
    if (composing) return;
    var value = element.val(),
        event = ev && ev.type;

    // By default we will trim the value
    // If the attribute ng-trim exists we will avoid trimming
    // If input type is 'password', the value is never trimmed
    if (type !== 'password' && (!attr.ngTrim || attr.ngTrim !== 'false')) {
      value = trim(value);
    }

    // If a control is suffering from bad input (due to native validators), browsers discard its
    // value, so it may be necessary to revalidate (by calling $setViewValue again) even if the
    // control's value is the same empty value twice in a row.
    if (ctrl.$viewValue !== value || (value === '' && ctrl.$$hasNativeValidators)) {
      ctrl.$setViewValue(value, event);
    }
  };

  // if the browser does support "input" event, we are fine - except on IE9 which doesn't fire the
  // input event on backspace, delete or cut
  if ($sniffer.hasEvent('input')) {
    element.on('input', listener);
  } else {
    var deferListener = function(ev, input, origValue) {
      if (!timeout) {
        timeout = $browser.defer(function() {
          timeout = null;
          if (!input || input.value !== origValue) {
            listener(ev);
          }
        });
      }
    };

    element.on('keydown', /** @this */ function(event) {
      var key = event.keyCode;

      // ignore
      //    command            modifiers                   arrows
      if (key === 91 || (15 < key && key < 19) || (37 <= key && key <= 40)) return;

      deferListener(event, this, this.value);
    });

    // if user modifies input value using context menu in IE, we need "paste" and "cut" events to catch it
    if ($sniffer.hasEvent('paste')) {
      element.on('paste cut', deferListener);
    }
  }

  // if user paste into input using mouse on older browser
  // or form autocomplete on newer browser, we need "change" event to catch it
  element.on('change', listener);

  // Some native input types (date-family) have the ability to change validity without
  // firing any input/change events.
  // For these event types, when native validators are present and the browser supports the type,
  // check for validity changes on various DOM events.
  if (PARTIAL_VALIDATION_TYPES[type] && ctrl.$$hasNativeValidators && type === attr.type) {
    element.on(PARTIAL_VALIDATION_EVENTS, /** @this */ function(ev) {
      if (!timeout) {
        var validity = this[VALIDITY_STATE_PROPERTY];
        var origBadInput = validity.badInput;
        var origTypeMismatch = validity.typeMismatch;
        timeout = $browser.defer(function() {
          timeout = null;
          if (validity.badInput !== origBadInput || validity.typeMismatch !== origTypeMismatch) {
            listener(ev);
          }
        });
      }
    });
  }

  ctrl.$render = function() {
    // Workaround for Firefox validation #12102.
    var value = ctrl.$isEmpty(ctrl.$viewValue) ? '' : ctrl.$viewValue;
    if (element.val() !== value) {
      element.val(value);
    }
  };
}

function weekParser(isoWeek, existingDate) {
  if (isDate(isoWeek)) {
    return isoWeek;
  }

  if (isString(isoWeek)) {
    WEEK_REGEXP.lastIndex = 0;
    var parts = WEEK_REGEXP.exec(isoWeek);
    if (parts) {
      var year = +parts[1],
          week = +parts[2],
          hours = 0,
          minutes = 0,
          seconds = 0,
          milliseconds = 0,
          firstThurs = getFirstThursdayOfYear(year),
          addDays = (week - 1) * 7;

      if (existingDate) {
        hours = existingDate.getHours();
        minutes = existingDate.getMinutes();
        seconds = existingDate.getSeconds();
        milliseconds = existingDate.getMilliseconds();
      }

      return new Date(year, 0, firstThurs.getDate() + addDays, hours, minutes, seconds, milliseconds);
    }
  }

  return NaN;
}

function createDateParser(regexp, mapping) {
  return function(iso, date) {
    var parts, map;

    if (isDate(iso)) {
      return iso;
    }

    if (isString(iso)) {
      // When a date is JSON'ified to wraps itself inside of an extra
      // set of double quotes. This makes the date parsing code unable
      // to match the date string and parse it as a date.
      if (iso.charAt(0) === '"' && iso.charAt(iso.length - 1) === '"') {
        iso = iso.substring(1, iso.length - 1);
      }
      if (ISO_DATE_REGEXP.test(iso)) {
        return new Date(iso);
      }
      regexp.lastIndex = 0;
      parts = regexp.exec(iso);

      if (parts) {
        parts.shift();
        if (date) {
          map = {
            yyyy: date.getFullYear(),
            MM: date.getMonth() + 1,
            dd: date.getDate(),
            HH: date.getHours(),
            mm: date.getMinutes(),
            ss: date.getSeconds(),
            sss: date.getMilliseconds() / 1000
          };
        } else {
          map = { yyyy: 1970, MM: 1, dd: 1, HH: 0, mm: 0, ss: 0, sss: 0 };
        }

        forEach(parts, function(part, index) {
          if (index < mapping.length) {
            map[mapping[index]] = +part;
          }
        });
        return new Date(map.yyyy, map.MM - 1, map.dd, map.HH, map.mm, map.ss || 0, map.sss * 1000 || 0);
      }
    }

    return NaN;
  };
}

function createDateInputType(type, regexp, parseDate, format) {
  return function dynamicDateInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter) {
    badInputChecker(scope, element, attr, ctrl);
    baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
    var timezone = ctrl && ctrl.$options.getOption('timezone');
    var previousDate;

    ctrl.$$parserName = type;
    ctrl.$parsers.push(function(value) {
      if (ctrl.$isEmpty(value)) return null;
      if (regexp.test(value)) {
        // Note: We cannot read ctrl.$modelValue, as there might be a different
        // parser/formatter in the processing chain so that the model
        // contains some different data format!
        var parsedDate = parseDate(value, previousDate);
        if (timezone) {
          parsedDate = convertTimezoneToLocal(parsedDate, timezone);
        }
        return parsedDate;
      }
      return undefined;
    });

    ctrl.$formatters.push(function(value) {
      if (value && !isDate(value)) {
        throw ngModelMinErr('datefmt', 'Expected `{0}` to be a date', value);
      }
      if (isValidDate(value)) {
        previousDate = value;
        if (previousDate && timezone) {
          previousDate = convertTimezoneToLocal(previousDate, timezone, true);
        }
        return $filter('date')(value, format, timezone);
      } else {
        previousDate = null;
        return '';
      }
    });

    if (isDefined(attr.min) || attr.ngMin) {
      var minVal;
      ctrl.$validators.min = function(value) {
        return !isValidDate(value) || isUndefined(minVal) || parseDate(value) >= minVal;
      };
      attr.$observe('min', function(val) {
        minVal = parseObservedDateValue(val);
        ctrl.$validate();
      });
    }

    if (isDefined(attr.max) || attr.ngMax) {
      var maxVal;
      ctrl.$validators.max = function(value) {
        return !isValidDate(value) || isUndefined(maxVal) || parseDate(value) <= maxVal;
      };
      attr.$observe('max', function(val) {
        maxVal = parseObservedDateValue(val);
        ctrl.$validate();
      });
    }

    function isValidDate(value) {
      // Invalid Date: getTime() returns NaN
      return value && !(value.getTime && value.getTime() !== value.getTime());
    }

    function parseObservedDateValue(val) {
      return isDefined(val) && !isDate(val) ? parseDate(val) || undefined : val;
    }
  };
}

function badInputChecker(scope, element, attr, ctrl) {
  var node = element[0];
  var nativeValidation = ctrl.$$hasNativeValidators = isObject(node.validity);
  if (nativeValidation) {
    ctrl.$parsers.push(function(value) {
      var validity = element.prop(VALIDITY_STATE_PROPERTY) || {};
      return validity.badInput || validity.typeMismatch ? undefined : value;
    });
  }
}

function numberFormatterParser(ctrl) {
  ctrl.$$parserName = 'number';
  ctrl.$parsers.push(function(value) {
    if (ctrl.$isEmpty(value))      return null;
    if (NUMBER_REGEXP.test(value)) return parseFloat(value);
    return undefined;
  });

  ctrl.$formatters.push(function(value) {
    if (!ctrl.$isEmpty(value)) {
      if (!isNumber(value)) {
        throw ngModelMinErr('numfmt', 'Expected `{0}` to be a number', value);
      }
      value = value.toString();
    }
    return value;
  });
}

function parseNumberAttrVal(val) {
  if (isDefined(val) && !isNumber(val)) {
    val = parseFloat(val);
  }
  return !isNumberNaN(val) ? val : undefined;
}

function isNumberInteger(num) {
  // See http://stackoverflow.com/questions/14636536/how-to-check-if-a-variable-is-an-integer-in-javascript#14794066
  // (minus the assumption that `num` is a number)

  // eslint-disable-next-line no-bitwise
  return (num | 0) === num;
}

function countDecimals(num) {
  var numString = num.toString();
  var decimalSymbolIndex = numString.indexOf('.');

  if (decimalSymbolIndex === -1) {
    if (-1 < num && num < 1) {
      // It may be in the exponential notation format (`1e-X`)
      var match = /e-(\d+)$/.exec(numString);

      if (match) {
        return Number(match[1]);
      }
    }

    return 0;
  }

  return numString.length - decimalSymbolIndex - 1;
}

function isValidForStep(viewValue, stepBase, step) {
  // At this point `stepBase` and `step` are expected to be non-NaN values
  // and `viewValue` is expected to be a valid stringified number.
  var value = Number(viewValue);

  var isNonIntegerValue = !isNumberInteger(value);
  var isNonIntegerStepBase = !isNumberInteger(stepBase);
  var isNonIntegerStep = !isNumberInteger(step);

  // Due to limitations in Floating Point Arithmetic (e.g. `0.3 - 0.2 !== 0.1` or
  // `0.5 % 0.1 !== 0`), we need to convert all numbers to integers.
  if (isNonIntegerValue || isNonIntegerStepBase || isNonIntegerStep) {
    var valueDecimals = isNonIntegerValue ? countDecimals(value) : 0;
    var stepBaseDecimals = isNonIntegerStepBase ? countDecimals(stepBase) : 0;
    var stepDecimals = isNonIntegerStep ? countDecimals(step) : 0;

    var decimalCount = Math.max(valueDecimals, stepBaseDecimals, stepDecimals);
    var multiplier = Math.pow(10, decimalCount);

    value = value * multiplier;
    stepBase = stepBase * multiplier;
    step = step * multiplier;

    if (isNonIntegerValue) value = Math.round(value);
    if (isNonIntegerStepBase) stepBase = Math.round(stepBase);
    if (isNonIntegerStep) step = Math.round(step);
  }

  return (value - stepBase) % step === 0;
}

function numberInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  badInputChecker(scope, element, attr, ctrl);
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var minVal;
  var maxVal;

  if (isDefined(attr.min) || attr.ngMin) {
    ctrl.$validators.min = function(value) {
      return ctrl.$isEmpty(value) || isUndefined(minVal) || value >= minVal;
    };

    attr.$observe('min', function(val) {
      minVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }

  if (isDefined(attr.max) || attr.ngMax) {
    ctrl.$validators.max = function(value) {
      return ctrl.$isEmpty(value) || isUndefined(maxVal) || value <= maxVal;
    };

    attr.$observe('max', function(val) {
      maxVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }

  if (isDefined(attr.step) || attr.ngStep) {
    var stepVal;
    ctrl.$validators.step = function(modelValue, viewValue) {
      return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
             isValidForStep(viewValue, minVal || 0, stepVal);
    };

    attr.$observe('step', function(val) {
      stepVal = parseNumberAttrVal(val);
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    });
  }
}

function rangeInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  badInputChecker(scope, element, attr, ctrl);
  numberFormatterParser(ctrl);
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);

  var supportsRange = ctrl.$$hasNativeValidators && element[0].type === 'range',
      minVal = supportsRange ? 0 : undefined,
      maxVal = supportsRange ? 100 : undefined,
      stepVal = supportsRange ? 1 : undefined,
      validity = element[0].validity,
      hasMinAttr = isDefined(attr.min),
      hasMaxAttr = isDefined(attr.max),
      hasStepAttr = isDefined(attr.step);

  var originalRender = ctrl.$render;

  ctrl.$render = supportsRange && isDefined(validity.rangeUnderflow) && isDefined(validity.rangeOverflow) ?
    //Browsers that implement range will set these values automatically, but reading the adjusted values after
    //$render would cause the min / max validators to be applied with the wrong value
    function rangeRender() {
      originalRender();
      ctrl.$setViewValue(element.val());
    } :
    originalRender;

  if (hasMinAttr) {
    ctrl.$validators.min = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMinValidator() { return true; } :
      // non-support browsers validate the min val
      function minValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(minVal) || viewValue >= minVal;
      };

    setInitialValueAndObserver('min', minChange);
  }

  if (hasMaxAttr) {
    ctrl.$validators.max = supportsRange ?
      // Since all browsers set the input to a valid value, we don't need to check validity
      function noopMaxValidator() { return true; } :
      // non-support browsers validate the max val
      function maxValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(maxVal) || viewValue <= maxVal;
      };

    setInitialValueAndObserver('max', maxChange);
  }

  if (hasStepAttr) {
    ctrl.$validators.step = supportsRange ?
      function nativeStepValidator() {
        // Currently, only FF implements the spec on step change correctly (i.e. adjusting the
        // input element value to a valid value). It's possible that other browsers set the stepMismatch
        // validity error instead, so we can at least report an error in that case.
        return !validity.stepMismatch;
      } :
      // ngStep doesn't set the setp attr, so the browser doesn't adjust the input value as setting step would
      function stepValidator(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || isUndefined(stepVal) ||
               isValidForStep(viewValue, minVal || 0, stepVal);
      };

    setInitialValueAndObserver('step', stepChange);
  }

  function setInitialValueAndObserver(htmlAttrName, changeFn) {
    // interpolated attributes set the attribute value only after a digest, but we need the
    // attribute value when the input is first rendered, so that the browser can adjust the
    // input value based on the min/max value
    element.attr(htmlAttrName, attr[htmlAttrName]);
    attr.$observe(htmlAttrName, changeFn);
  }

  function minChange(val) {
    minVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the minVal is greater than the element value
      if (minVal > elVal) {
        elVal = minVal;
        element.val(elVal);
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function maxChange(val) {
    maxVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    if (supportsRange) {
      var elVal = element.val();
      // IE11 doesn't set the el val correctly if the maxVal is less than the element value
      if (maxVal < elVal) {
        element.val(maxVal);
        // IE11 and Chrome don't set the value to the minVal when max < min
        elVal = maxVal < minVal ? minVal : maxVal;
      }
      ctrl.$setViewValue(elVal);
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }

  function stepChange(val) {
    stepVal = parseNumberAttrVal(val);
    // ignore changes before model is initialized
    if (isNumberNaN(ctrl.$modelValue)) {
      return;
    }

    // Some browsers don't adjust the input value correctly, but set the stepMismatch error
    if (supportsRange && ctrl.$viewValue !== element.val()) {
      ctrl.$setViewValue(element.val());
    } else {
      // TODO(matsko): implement validateLater to reduce number of validations
      ctrl.$validate();
    }
  }
}

function urlInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$$parserName = 'url';
  ctrl.$validators.url = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || URL_REGEXP.test(value);
  };
}

function emailInputType(scope, element, attr, ctrl, $sniffer, $browser) {
  // Note: no badInputChecker here by purpose as `url` is only a validation
  // in browsers, i.e. we can always read out input.value even if it is not valid!
  baseInputType(scope, element, attr, ctrl, $sniffer, $browser);
  stringBasedInputType(ctrl);

  ctrl.$$parserName = 'email';
  ctrl.$validators.email = function(modelValue, viewValue) {
    var value = modelValue || viewValue;
    return ctrl.$isEmpty(value) || EMAIL_REGEXP.test(value);
  };
}

function radioInputType(scope, element, attr, ctrl) {
  var doTrim = !attr.ngTrim || trim(attr.ngTrim) !== 'false';
  // make the name unique, if not defined
  if (isUndefined(attr.name)) {
    element.attr('name', nextUid());
  }

  var listener = function(ev) {
    var value;
    if (element[0].checked) {
      value = attr.value;
      if (doTrim) {
        value = trim(value);
      }
      ctrl.$setViewValue(value, ev && ev.type);
    }
  };

  element.on('click', listener);

  ctrl.$render = function() {
    var value = attr.value;
    if (doTrim) {
      value = trim(value);
    }
    element[0].checked = (value === ctrl.$viewValue);
  };

  attr.$observe('value', ctrl.$render);
}

function parseConstantExpr($parse, context, name, expression, fallback) {
  var parseFn;
  if (isDefined(expression)) {
    parseFn = $parse(expression);
    if (!parseFn.constant) {
      throw ngModelMinErr('constexpr', 'Expected constant expression for `{0}`, but saw ' +
                                   '`{1}`.', name, expression);
    }
    return parseFn(context);
  }
  return fallback;
}

function checkboxInputType(scope, element, attr, ctrl, $sniffer, $browser, $filter, $parse) {
  var trueValue = parseConstantExpr($parse, scope, 'ngTrueValue', attr.ngTrueValue, true);
  var falseValue = parseConstantExpr($parse, scope, 'ngFalseValue', attr.ngFalseValue, false);

  var listener = function(ev) {
    ctrl.$setViewValue(element[0].checked, ev && ev.type);
  };

  element.on('click', listener);

  ctrl.$render = function() {
    element[0].checked = ctrl.$viewValue;
  };

  // Override the standard `$isEmpty` because the $viewValue of an empty checkbox is always set to `false`
  // This is because of the parser below, which compares the `$modelValue` with `trueValue` to convert
  // it to a boolean.
  ctrl.$isEmpty = function(value) {
    return value === false;
  };

  ctrl.$formatters.push(function(value) {
    return equals(value, trueValue);
  });

  ctrl.$parsers.push(function(value) {
    return value ? trueValue : falseValue;
  });
}


/**
 * @ngdoc directive
 * @name textarea
 * @restrict E
 *
 * @description
 * HTML textarea element control with angular data-binding. The data-binding and validation
 * properties of this element are exactly the same as those of the
 * {@link ng.directive:input input element}.
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    does not match a RegExp found by evaluating the Angular expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange Angular expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
 *
 * @knownIssue
 *
 * When specifying the `placeholder` attribute of `<textarea>`, Internet Explorer will temporarily
 * insert the placeholder value as the textarea's content. If the placeholder value contains
 * interpolation (`{{ ... }}`), an error will be logged in the console when Angular tries to update
 * the value of the by-then-removed text node. This doesn't affect the functionality of the
 * textarea, but can be undesirable.
 *
 * You can work around this Internet Explorer issue by using `ng-attr-placeholder` instead of
 * `placeholder` on textareas, whenever you need interpolation in the placeholder value. You can
 * find more details on `ngAttr` in the
 * [Interpolation](guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes) section of the
 * Developer Guide.
 */


/**
 * @ngdoc directive
 * @name input
 * @restrict E
 *
 * @description
 * HTML input element control. When used together with {@link ngModel `ngModel`}, it provides data-binding,
 * input state control, and validation.
 * Input control follows HTML5 input types and polyfills the HTML5 validation behavior for older browsers.
 *
 * <div class="alert alert-warning">
 * **Note:** Not every feature offered is available for all input types.
 * Specifically, data binding and event handling via `ng-model` is unsupported for `input[file]`.
 * </div>
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {boolean=} ngRequired Sets `required` attribute if set to true
 * @param {number=} ngMinlength Sets `minlength` validation error key if the value is shorter than
 *    minlength.
 * @param {number=} ngMaxlength Sets `maxlength` validation error key if the value is longer than
 *    maxlength. Setting the attribute to a negative or non-numeric value, allows view values of any
 *    length.
 * @param {string=} ngPattern Sets `pattern` validation error key if the ngModel {@link ngModel.NgModelController#$viewValue $viewValue}
 *    value does not match a RegExp found by evaluating the Angular expression given in the attribute value.
 *    If the expression evaluates to a RegExp object, then this is used directly.
 *    If the expression evaluates to a string, then it will be converted to a RegExp
 *    after wrapping it in `^` and `$` characters. For instance, `"abc"` will be converted to
 *    `new RegExp('^abc$')`.<br />
 *    **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 *    start at the index of the last search's match, thus not taking the whole input value into
 *    account.
 * @param {string=} ngChange Angular expression to be executed when input changes due to user
 *    interaction with the input element.
 * @param {boolean=} [ngTrim=true] If set to false Angular will not automatically trim the input.
 *    This parameter is ignored for input[type=password] controls, which will never trim the
 *    input.
 *
 * @example
    <example name="input-directive" module="inputExample">
      <file name="index.html">
       <script>
          angular.module('inputExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.user = {name: 'guest', last: 'visitor'};
            }]);
       </script>
       <div ng-controller="ExampleController">
         <form name="myForm">
           <label>
              User name:
              <input type="text" name="userName" ng-model="user.name" required>
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.userName.$error.required">
              Required!</span>
           </div>
           <label>
              Last name:
              <input type="text" name="lastName" ng-model="user.last"
              ng-minlength="3" ng-maxlength="10">
           </label>
           <div role="alert">
             <span class="error" ng-show="myForm.lastName.$error.minlength">
               Too short!</span>
             <span class="error" ng-show="myForm.lastName.$error.maxlength">
               Too long!</span>
           </div>
         </form>
         <hr>
         <tt>user = {{user}}</tt><br/>
         <tt>myForm.userName.$valid = {{myForm.userName.$valid}}</tt><br/>
         <tt>myForm.userName.$error = {{myForm.userName.$error}}</tt><br/>
         <tt>myForm.lastName.$valid = {{myForm.lastName.$valid}}</tt><br/>
         <tt>myForm.lastName.$error = {{myForm.lastName.$error}}</tt><br/>
         <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
         <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
         <tt>myForm.$error.minlength = {{!!myForm.$error.minlength}}</tt><br/>
         <tt>myForm.$error.maxlength = {{!!myForm.$error.maxlength}}</tt><br/>
       </div>
      </file>
      <file name="protractor.js" type="protractor">
        var user = element(by.exactBinding('user'));
        var userNameValid = element(by.binding('myForm.userName.$valid'));
        var lastNameValid = element(by.binding('myForm.lastName.$valid'));
        var lastNameError = element(by.binding('myForm.lastName.$error'));
        var formValid = element(by.binding('myForm.$valid'));
        var userNameInput = element(by.model('user.name'));
        var userLastInput = element(by.model('user.last'));

        it('should initialize to model', function() {
          expect(user.getText()).toContain('{"name":"guest","last":"visitor"}');
          expect(userNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if empty when required', function() {
          userNameInput.clear();
          userNameInput.sendKeys('');

          expect(user.getText()).toContain('{"last":"visitor"}');
          expect(userNameValid.getText()).toContain('false');
          expect(formValid.getText()).toContain('false');
        });

        it('should be valid if empty when min length is set', function() {
          userLastInput.clear();
          userLastInput.sendKeys('');

          expect(user.getText()).toContain('{"name":"guest","last":""}');
          expect(lastNameValid.getText()).toContain('true');
          expect(formValid.getText()).toContain('true');
        });

        it('should be invalid if less than required min length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('xx');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('minlength');
          expect(formValid.getText()).toContain('false');
        });

        it('should be invalid if longer than max length', function() {
          userLastInput.clear();
          userLastInput.sendKeys('some ridiculously long name');

          expect(user.getText()).toContain('{"name":"guest"}');
          expect(lastNameValid.getText()).toContain('false');
          expect(lastNameError.getText()).toContain('maxlength');
          expect(formValid.getText()).toContain('false');
        });
      </file>
    </example>
 */
var inputDirective = ['$browser', '$sniffer', '$filter', '$parse',
    function($browser, $sniffer, $filter, $parse) {
  return {
    restrict: 'E',
    require: ['?ngModel'],
    link: {
      pre: function(scope, element, attr, ctrls) {
        if (ctrls[0]) {
          (inputType[lowercase(attr.type)] || inputType.text)(scope, element, attr, ctrls[0], $sniffer,
                                                              $browser, $filter, $parse);
        }
      }
    }
  };
}];



var CONSTANT_VALUE_REGEXP = /^(true|false|\d+)$/;
/**
 * @ngdoc directive
 * @name ngValue
 *
 * @description
 * Binds the given expression to the value of the element.
 *
 * It is mainly used on {@link input[radio] `input[radio]`} and option elements,
 * so that when the element is selected, the {@link ngModel `ngModel`} of that element (or its
 * {@link select `select`} parent element) is set to the bound value. It is especially useful
 * for dynamically generated lists using {@link ngRepeat `ngRepeat`}, as shown below.
 *
 * It can also be used to achieve one-way binding of a given expression to an input element
 * such as an `input[text]` or a `textarea`, when that element does not use ngModel.
 *
 * @element input
 * @param {string=} ngValue angular expression, whose value will be bound to the `value` attribute
 * and `value` property of the element.
 *
 * @example
    <example name="ngValue-directive" module="valueExample">
      <file name="index.html">
       <script>
          angular.module('valueExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.names = ['pizza', 'unicorns', 'robots'];
              $scope.my = { favorite: 'unicorns' };
            }]);
       </script>
        <form ng-controller="ExampleController">
          <h2>Which is your favorite?</h2>
            <label ng-repeat="name in names" for="{{name}}">
              {{name}}
              <input type="radio"
                     ng-model="my.favorite"
                     ng-value="name"
                     id="{{name}}"
                     name="favorite">
            </label>
          <div>You chose {{my.favorite}}</div>
        </form>
      </file>
      <file name="protractor.js" type="protractor">
        var favorite = element(by.binding('my.favorite'));

        it('should initialize to model', function() {
          expect(favorite.getText()).toContain('unicorns');
        });
        it('should bind the values to the inputs', function() {
          element.all(by.model('my.favorite')).get(0).click();
          expect(favorite.getText()).toContain('pizza');
        });
      </file>
    </example>
 */
var ngValueDirective = function() {
  /**
   *  inputs use the value attribute as their default value if the value property is not set.
   *  Once the value property has been set (by adding input), it will not react to changes to
   *  the value attribute anymore. Setting both attribute and property fixes this behavior, and
   *  makes it possible to use ngValue as a sort of one-way bind.
   */
  function updateElementValue(element, attr, value) {
    // Support: IE9 only
    // In IE9 values are converted to string (e.g. `input.value = null` results in `input.value === 'null'`).
    var propValue = isDefined(value) ? value : (msie === 9) ? '' : null;
    element.prop('value', propValue);
    attr.$set('value', value);
  }

  return {
    restrict: 'A',
    priority: 100,
    compile: function(tpl, tplAttr) {
      if (CONSTANT_VALUE_REGEXP.test(tplAttr.ngValue)) {
        return function ngValueConstantLink(scope, elm, attr) {
          var value = scope.$eval(attr.ngValue);
          updateElementValue(elm, attr, value);
        };
      } else {
        return function ngValueLink(scope, elm, attr) {
          scope.$watch(attr.ngValue, function valueWatchAction(value) {
            updateElementValue(elm, attr, value);
          });
        };
      }
    }
  };
};

/**
 * @ngdoc directive
 * @name ngBind
 * @restrict AC
 *
 * @description
 * The `ngBind` attribute tells Angular to replace the text content of the specified HTML element
 * with the value of a given expression, and to update the text content when the value of that
 * expression changes.
 *
 * Typically, you don't use `ngBind` directly, but instead you use the double curly markup like
 * `{{ expression }}` which is similar but less verbose.
 *
 * It is preferable to use `ngBind` instead of `{{ expression }}` if a template is momentarily
 * displayed by the browser in its raw state before Angular compiles it. Since `ngBind` is an
 * element attribute, it makes the bindings invisible to the user while the page is loading.
 *
 * An alternative solution to this problem would be using the
 * {@link ng.directive:ngCloak ngCloak} directive.
 *
 *
 * @element ANY
 * @param {expression} ngBind {@link guide/expression Expression} to evaluate.
 *
 * @example
 * Enter a name in the Live Preview text box; the greeting below the text box changes instantly.
   <example module="bindExample" name="ng-bind">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.name = 'Whirled';
           }]);
       </script>
       <div ng-controller="ExampleController">
         <label>Enter name: <input type="text" ng-model="name"></label><br>
         Hello <span ng-bind="name"></span>!
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var nameInput = element(by.model('name'));

         expect(element(by.binding('name')).getText()).toBe('Whirled');
         nameInput.clear();
         nameInput.sendKeys('world');
         expect(element(by.binding('name')).getText()).toBe('world');
       });
     </file>
   </example>
 */
var ngBindDirective = ['$compile', function($compile) {
  return {
    restrict: 'AC',
    compile: function ngBindCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBind);
        element = element[0];
        scope.$watch(attr.ngBind, function ngBindWatchAction(value) {
          element.textContent = stringify(value);
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindTemplate
 *
 * @description
 * The `ngBindTemplate` directive specifies that the element
 * text content should be replaced with the interpolation of the template
 * in the `ngBindTemplate` attribute.
 * Unlike `ngBind`, the `ngBindTemplate` can contain multiple `{{` `}}`
 * expressions. This directive is needed since some HTML elements
 * (such as TITLE and OPTION) cannot contain SPAN elements.
 *
 * @element ANY
 * @param {string} ngBindTemplate template of form
 *   <tt>{{</tt> <tt>expression</tt> <tt>}}</tt> to eval.
 *
 * @example
 * Try it here: enter text in text box and watch the greeting change.
   <example module="bindExample" name="ng-bind-template">
     <file name="index.html">
       <script>
         angular.module('bindExample', [])
           .controller('ExampleController', ['$scope', function($scope) {
             $scope.salutation = 'Hello';
             $scope.name = 'World';
           }]);
       </script>
       <div ng-controller="ExampleController">
        <label>Salutation: <input type="text" ng-model="salutation"></label><br>
        <label>Name: <input type="text" ng-model="name"></label><br>
        <pre ng-bind-template="{{salutation}} {{name}}!"></pre>
       </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-bind', function() {
         var salutationElem = element(by.binding('salutation'));
         var salutationInput = element(by.model('salutation'));
         var nameInput = element(by.model('name'));

         expect(salutationElem.getText()).toBe('Hello World!');

         salutationInput.clear();
         salutationInput.sendKeys('Greetings');
         nameInput.clear();
         nameInput.sendKeys('user');

         expect(salutationElem.getText()).toBe('Greetings user!');
       });
     </file>
   </example>
 */
var ngBindTemplateDirective = ['$interpolate', '$compile', function($interpolate, $compile) {
  return {
    compile: function ngBindTemplateCompile(templateElement) {
      $compile.$$addBindingClass(templateElement);
      return function ngBindTemplateLink(scope, element, attr) {
        var interpolateFn = $interpolate(element.attr(attr.$attr.ngBindTemplate));
        $compile.$$addBindingInfo(element, interpolateFn.expressions);
        element = element[0];
        attr.$observe('ngBindTemplate', function(value) {
          element.textContent = isUndefined(value) ? '' : value;
        });
      };
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngBindHtml
 *
 * @description
 * Evaluates the expression and inserts the resulting HTML into the element in a secure way. By default,
 * the resulting HTML content will be sanitized using the {@link ngSanitize.$sanitize $sanitize} service.
 * To utilize this functionality, ensure that `$sanitize` is available, for example, by including {@link
 * ngSanitize} in your module's dependencies (not in core Angular). In order to use {@link ngSanitize}
 * in your module's dependencies, you need to include "angular-sanitize.js" in your application.
 *
 * You may also bypass sanitization for values you know are safe. To do so, bind to
 * an explicitly trusted value via {@link ng.$sce#trustAsHtml $sce.trustAsHtml}.  See the example
 * under {@link ng.$sce#show-me-an-example-using-sce- Strict Contextual Escaping (SCE)}.
 *
 * Note: If a `$sanitize` service is unavailable and the bound value isn't explicitly trusted, you
 * will have an exception (instead of an exploit.)
 *
 * @element ANY
 * @param {expression} ngBindHtml {@link guide/expression Expression} to evaluate.
 *
 * @example

   <example module="bindHtmlExample" deps="angular-sanitize.js" name="ng-bind-html">
     <file name="index.html">
       <div ng-controller="ExampleController">
        <p ng-bind-html="myHTML"></p>
       </div>
     </file>

     <file name="script.js">
       angular.module('bindHtmlExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.myHTML =
              'I am an <code>HTML</code>string with ' +
              '<a href="#">links!</a> and other <em>stuff</em>';
         }]);
     </file>

     <file name="protractor.js" type="protractor">
       it('should check ng-bind-html', function() {
         expect(element(by.binding('myHTML')).getText()).toBe(
             'I am an HTMLstring with links! and other stuff');
       });
     </file>
   </example>
 */
var ngBindHtmlDirective = ['$sce', '$parse', '$compile', function($sce, $parse, $compile) {
  return {
    restrict: 'A',
    compile: function ngBindHtmlCompile(tElement, tAttrs) {
      var ngBindHtmlGetter = $parse(tAttrs.ngBindHtml);
      var ngBindHtmlWatch = $parse(tAttrs.ngBindHtml, function sceValueOf(val) {
        // Unwrap the value to compare the actual inner safe value, not the wrapper object.
        return $sce.valueOf(val);
      });
      $compile.$$addBindingClass(tElement);

      return function ngBindHtmlLink(scope, element, attr) {
        $compile.$$addBindingInfo(element, attr.ngBindHtml);

        scope.$watch(ngBindHtmlWatch, function ngBindHtmlWatchAction() {
          // The watched value is the unwrapped value. To avoid re-escaping, use the direct getter.
          var value = ngBindHtmlGetter(scope);
          element.html($sce.getTrustedHtml(value) || '');
        });
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngChange
 *
 * @description
 * Evaluate the given expression when the user changes the input.
 * The expression is evaluated immediately, unlike the JavaScript onchange event
 * which only triggers at the end of a change (usually, when the user leaves the
 * form element or presses the return key).
 *
 * The `ngChange` expression is only evaluated when a change in the input value causes
 * a new value to be committed to the model.
 *
 * It will not be evaluated:
 * * if the value returned from the `$parsers` transformation pipeline has not changed
 * * if the input has continued to be invalid since the model will stay `null`
 * * if the model is changed programmatically and not by a change to the input value
 *
 *
 * Note, this directive requires `ngModel` to be present.
 *
 * @element input
 * @param {expression} ngChange {@link guide/expression Expression} to evaluate upon change
 * in input value.
 *
 * @example
 * <example name="ngChange-directive" module="changeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('changeExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.counter = 0;
 *           $scope.change = function() {
 *             $scope.counter++;
 *           };
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input type="checkbox" ng-model="confirmed" ng-change="change()" id="ng-change-example1" />
 *       <input type="checkbox" ng-model="confirmed" id="ng-change-example2" />
 *       <label for="ng-change-example2">Confirmed</label><br />
 *       <tt>debug = {{confirmed}}</tt><br/>
 *       <tt>counter = {{counter}}</tt><br/>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var counter = element(by.binding('counter'));
 *     var debug = element(by.binding('confirmed'));
 *
 *     it('should evaluate the expression if changing from view', function() {
 *       expect(counter.getText()).toContain('0');
 *
 *       element(by.id('ng-change-example1')).click();
 *
 *       expect(counter.getText()).toContain('1');
 *       expect(debug.getText()).toContain('true');
 *     });
 *
 *     it('should not evaluate the expression if changing from model', function() {
 *       element(by.id('ng-change-example2')).click();

 *       expect(counter.getText()).toContain('0');
 *       expect(debug.getText()).toContain('true');
 *     });
 *   </file>
 * </example>
 */
var ngChangeDirective = valueFn({
  restrict: 'A',
  require: 'ngModel',
  link: function(scope, element, attr, ctrl) {
    ctrl.$viewChangeListeners.push(function() {
      scope.$eval(attr.ngChange);
    });
  }
});

/* exported
  ngClassDirective,
  ngClassEvenDirective,
  ngClassOddDirective
*/

function classDirective(name, selector) {
  name = 'ngClass' + name;
  var indexWatchExpression;

  return ['$parse', function($parse) {
    return {
      restrict: 'AC',
      link: function(scope, element, attr) {
        var expression = attr[name].trim();
        var isOneTime = (expression.charAt(0) === ':') && (expression.charAt(1) === ':');

        var watchInterceptor = isOneTime ? toFlatValue : toClassString;
        var watchExpression = $parse(expression, watchInterceptor);
        var watchAction = isOneTime ? ngClassOneTimeWatchAction : ngClassWatchAction;

        var classCounts = element.data('$classCounts');
        var oldModulo = true;
        var oldClassString;

        if (!classCounts) {
          // Use createMap() to prevent class assumptions involving property
          // names in Object.prototype
          classCounts = createMap();
          element.data('$classCounts', classCounts);
        }

        if (name !== 'ngClass') {
          if (!indexWatchExpression) {
            indexWatchExpression = $parse('$index', function moduloTwo($index) {
              // eslint-disable-next-line no-bitwise
              return $index & 1;
            });
          }

          scope.$watch(indexWatchExpression, ngClassIndexWatchAction);
        }

        scope.$watch(watchExpression, watchAction, isOneTime);

        function addClasses(classString) {
          classString = digestClassCounts(split(classString), 1);
          attr.$addClass(classString);
        }

        function removeClasses(classString) {
          classString = digestClassCounts(split(classString), -1);
          attr.$removeClass(classString);
        }

        function updateClasses(oldClassString, newClassString) {
          var oldClassArray = split(oldClassString);
          var newClassArray = split(newClassString);

          var toRemoveArray = arrayDifference(oldClassArray, newClassArray);
          var toAddArray = arrayDifference(newClassArray, oldClassArray);

          var toRemoveString = digestClassCounts(toRemoveArray, -1);
          var toAddString = digestClassCounts(toAddArray, 1);

          attr.$addClass(toAddString);
          attr.$removeClass(toRemoveString);
        }

        function digestClassCounts(classArray, count) {
          var classesToUpdate = [];

          forEach(classArray, function(className) {
            if (count > 0 || classCounts[className]) {
              classCounts[className] = (classCounts[className] || 0) + count;
              if (classCounts[className] === +(count > 0)) {
                classesToUpdate.push(className);
              }
            }
          });

          return classesToUpdate.join(' ');
        }

        function ngClassIndexWatchAction(newModulo) {
          // This watch-action should run before the `ngClass[OneTime]WatchAction()`, thus it
          // adds/removes `oldClassString`. If the `ngClass` expression has changed as well, the
          // `ngClass[OneTime]WatchAction()` will update the classes.
          if (newModulo === selector) {
            addClasses(oldClassString);
          } else {
            removeClasses(oldClassString);
          }

          oldModulo = newModulo;
        }

        function ngClassOneTimeWatchAction(newClassValue) {
          var newClassString = toClassString(newClassValue);

          if (newClassString !== oldClassString) {
            ngClassWatchAction(newClassString);
          }
        }

        function ngClassWatchAction(newClassString) {
          if (oldModulo === selector) {
            updateClasses(oldClassString, newClassString);
          }

          oldClassString = newClassString;
        }
      }
    };
  }];

  // Helpers
  function arrayDifference(tokens1, tokens2) {
    if (!tokens1 || !tokens1.length) return [];
    if (!tokens2 || !tokens2.length) return tokens1;

    var values = [];

    outer:
    for (var i = 0; i < tokens1.length; i++) {
      var token = tokens1[i];
      for (var j = 0; j < tokens2.length; j++) {
        if (token === tokens2[j]) continue outer;
      }
      values.push(token);
    }

    return values;
  }

  function split(classString) {
    return classString && classString.split(' ');
  }

  function toClassString(classValue) {
    var classString = classValue;

    if (isArray(classValue)) {
      classString = classValue.map(toClassString).join(' ');
    } else if (isObject(classValue)) {
      classString = Object.keys(classValue).
        filter(function(key) { return classValue[key]; }).
        join(' ');
    }

    return classString;
  }

  function toFlatValue(classValue) {
    var flatValue = classValue;

    if (isArray(classValue)) {
      flatValue = classValue.map(toFlatValue);
    } else if (isObject(classValue)) {
      var hasUndefined = false;

      flatValue = Object.keys(classValue).filter(function(key) {
        var value = classValue[key];

        if (!hasUndefined && isUndefined(value)) {
          hasUndefined = true;
        }

        return value;
      });

      if (hasUndefined) {
        // Prevent the `oneTimeLiteralWatchInterceptor` from unregistering
        // the watcher, by including at least one `undefined` value.
        flatValue.push(undefined);
      }
    }

    return flatValue;
  }
}

/**
 * @ngdoc directive
 * @name ngClass
 * @restrict AC
 *
 * @description
 * The `ngClass` directive allows you to dynamically set CSS classes on an HTML element by databinding
 * an expression that represents all classes to be added.
 *
 * The directive operates in three different ways, depending on which of three types the expression
 * evaluates to:
 *
 * 1. If the expression evaluates to a string, the string should be one or more space-delimited class
 * names.
 *
 * 2. If the expression evaluates to an object, then for each key-value pair of the
 * object with a truthy value the corresponding key is used as a class name.
 *
 * 3. If the expression evaluates to an array, each element of the array should either be a string as in
 * type 1 or an object as in type 2. This means that you can mix strings and objects together in an array
 * to give you more control over what CSS classes appear. See the code below for an example of this.
 *
 *
 * The directive won't add duplicate classes if a particular class was already set.
 *
 * When the expression changes, the previously added classes are removed and only then are the
 * new classes added.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `class`
 * attribute, when using the `ngClass` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#addClass addClass}       | just before the class is applied to the element   |
 * | {@link ng.$animate#removeClass removeClass} | just before the class is removed from the element |
 *
 * @element ANY
 * @param {expression} ngClass {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class
 *   names, an array, or a map of class names to boolean values. In the case of a map, the
 *   names of the properties whose values are truthy will be added as css classes to the
 *   element.
 *
 * @example Example that demonstrates basic bindings via ngClass directive.
   <example name="ng-class">
     <file name="index.html">
       <p ng-class="{strike: deleted, bold: important, 'has-error': error}">Map Syntax Example</p>
       <label>
          <input type="checkbox" ng-model="deleted">
          deleted (apply "strike" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="important">
          important (apply "bold" class)
       </label><br>
       <label>
          <input type="checkbox" ng-model="error">
          error (apply "has-error" class)
       </label>
       <hr>
       <p ng-class="style">Using String Syntax</p>
       <input type="text" ng-model="style"
              placeholder="Type: bold strike red" aria-label="Type: bold strike red">
       <hr>
       <p ng-class="[style1, style2, style3]">Using Array Syntax</p>
       <input ng-model="style1"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red"><br>
       <input ng-model="style2"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 2"><br>
       <input ng-model="style3"
              placeholder="Type: bold, strike or red" aria-label="Type: bold, strike or red 3"><br>
       <hr>
       <p ng-class="[style4, {orange: warning}]">Using Array and Map Syntax</p>
       <input ng-model="style4" placeholder="Type: bold, strike" aria-label="Type: bold, strike"><br>
       <label><input type="checkbox" ng-model="warning"> warning (apply "orange" class)</label>
     </file>
     <file name="style.css">
       .strike {
           text-decoration: line-through;
       }
       .bold {
           font-weight: bold;
       }
       .red {
           color: red;
       }
       .has-error {
           color: red;
           background-color: yellow;
       }
       .orange {
           color: orange;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var ps = element.all(by.css('p'));

       it('should let you toggle the class', function() {

         expect(ps.first().getAttribute('class')).not.toMatch(/bold/);
         expect(ps.first().getAttribute('class')).not.toMatch(/has-error/);

         element(by.model('important')).click();
         expect(ps.first().getAttribute('class')).toMatch(/bold/);

         element(by.model('error')).click();
         expect(ps.first().getAttribute('class')).toMatch(/has-error/);
       });

       it('should let you toggle string example', function() {
         expect(ps.get(1).getAttribute('class')).toBe('');
         element(by.model('style')).clear();
         element(by.model('style')).sendKeys('red');
         expect(ps.get(1).getAttribute('class')).toBe('red');
       });

       it('array example should have 3 classes', function() {
         expect(ps.get(2).getAttribute('class')).toBe('');
         element(by.model('style1')).sendKeys('bold');
         element(by.model('style2')).sendKeys('strike');
         element(by.model('style3')).sendKeys('red');
         expect(ps.get(2).getAttribute('class')).toBe('bold strike red');
       });

       it('array with map example should have 2 classes', function() {
         expect(ps.last().getAttribute('class')).toBe('');
         element(by.model('style4')).sendKeys('bold');
         element(by.model('warning')).click();
         expect(ps.last().getAttribute('class')).toBe('bold orange');
       });
     </file>
   </example>

   ## Animations

   The example below demonstrates how to perform animations using ngClass.

   <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-class">
     <file name="index.html">
      <input id="setbtn" type="button" value="set" ng-click="myVar='my-class'">
      <input id="clearbtn" type="button" value="clear" ng-click="myVar=''">
      <br>
      <span class="base-class" ng-class="myVar">Sample Text</span>
     </file>
     <file name="style.css">
       .base-class {
         transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
       }

       .base-class.my-class {
         color: red;
         font-size:3em;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class', function() {
         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);

         element(by.id('setbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).
           toMatch(/my-class/);

         element(by.id('clearbtn')).click();

         expect(element(by.css('.base-class')).getAttribute('class')).not.
           toMatch(/my-class/);
       });
     </file>
   </example>


   ## ngClass and pre-existing CSS3 Transitions/Animations
   The ngClass directive still supports CSS3 Transitions/Animations even if they do not follow the ngAnimate CSS naming structure.
   Upon animation ngAnimate will apply supplementary CSS classes to track the start and end of an animation, but this will not hinder
   any pre-existing CSS transitions already on the element. To get an idea of what happens during a class-based animation, be sure
   to view the step by step details of {@link $animate#addClass $animate.addClass} and
   {@link $animate#removeClass $animate.removeClass}.
 */
var ngClassDirective = classDirective('', true);

/**
 * @ngdoc directive
 * @name ngClassOdd
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @element ANY
 * @param {expression} ngClassOdd {@link guide/expression Expression} to eval. The result
 *   of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-odd">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}}
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 */
var ngClassOddDirective = classDirective('Odd', 0);

/**
 * @ngdoc directive
 * @name ngClassEven
 * @restrict AC
 *
 * @description
 * The `ngClassOdd` and `ngClassEven` directives work exactly as
 * {@link ng.directive:ngClass ngClass}, except they work in
 * conjunction with `ngRepeat` and take effect only on odd (even) rows.
 *
 * This directive can be applied only within the scope of an
 * {@link ng.directive:ngRepeat ngRepeat}.
 *
 * @element ANY
 * @param {expression} ngClassEven {@link guide/expression Expression} to eval. The
 *   result of the evaluation can be a string representing space delimited class names or an array.
 *
 * @example
   <example name="ng-class-even">
     <file name="index.html">
        <ol ng-init="names=['John', 'Mary', 'Cate', 'Suz']">
          <li ng-repeat="name in names">
           <span ng-class-odd="'odd'" ng-class-even="'even'">
             {{name}} &nbsp; &nbsp; &nbsp;
           </span>
          </li>
        </ol>
     </file>
     <file name="style.css">
       .odd {
         color: red;
       }
       .even {
         color: blue;
       }
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-class-odd and ng-class-even', function() {
         expect(element(by.repeater('name in names').row(0).column('name')).getAttribute('class')).
           toMatch(/odd/);
         expect(element(by.repeater('name in names').row(1).column('name')).getAttribute('class')).
           toMatch(/even/);
       });
     </file>
   </example>
 */
var ngClassEvenDirective = classDirective('Even', 1);

/**
 * @ngdoc directive
 * @name ngCloak
 * @restrict AC
 *
 * @description
 * The `ngCloak` directive is used to prevent the Angular html template from being briefly
 * displayed by the browser in its raw (uncompiled) form while your application is loading. Use this
 * directive to avoid the undesirable flicker effect caused by the html template display.
 *
 * The directive can be applied to the `<body>` element, but the preferred usage is to apply
 * multiple `ngCloak` directives to small portions of the page to permit progressive rendering
 * of the browser view.
 *
 * `ngCloak` works in cooperation with the following css rule embedded within `angular.js` and
 * `angular.min.js`.
 * For CSP mode please add `angular-csp.css` to your html file (see {@link ng.directive:ngCsp ngCsp}).
 *
 * ```css
 * [ng\:cloak], [ng-cloak], [data-ng-cloak], [x-ng-cloak], .ng-cloak, .x-ng-cloak {
 *   display: none !important;
 * }
 * ```
 *
 * When this css rule is loaded by the browser, all html elements (including their children) that
 * are tagged with the `ngCloak` directive are hidden. When Angular encounters this directive
 * during the compilation of the template it deletes the `ngCloak` element attribute, making
 * the compiled element visible.
 *
 * For the best result, the `angular.js` script must be loaded in the head section of the html
 * document; alternatively, the css rule above must be included in the external stylesheet of the
 * application.
 *
 * @element ANY
 *
 * @example
   <example name="ng-cloak">
     <file name="index.html">
        <div id="template1" ng-cloak>{{ 'hello' }}</div>
        <div id="template2" class="ng-cloak">{{ 'world' }}</div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should remove the template directive and css class', function() {
         expect($('#template1').getAttribute('ng-cloak')).
           toBeNull();
         expect($('#template2').getAttribute('ng-cloak')).
           toBeNull();
       });
     </file>
   </example>
 *
 */
var ngCloakDirective = ngDirective({
  compile: function(element, attr) {
    attr.$set('ngCloak', undefined);
    element.removeClass('ng-cloak');
  }
});

/**
 * @ngdoc directive
 * @name ngController
 *
 * @description
 * The `ngController` directive attaches a controller class to the view. This is a key aspect of how angular
 * supports the principles behind the Model-View-Controller design pattern.
 *
 * MVC components in angular:
 *
 * * Model — Models are the properties of a scope; scopes are attached to the DOM where scope properties
 *   are accessed through bindings.
 * * View — The template (HTML with data bindings) that is rendered into the View.
 * * Controller — The `ngController` directive specifies a Controller class; the class contains business
 *   logic behind the application to decorate the scope with functions and values
 *
 * Note that you can also attach controllers to the DOM by declaring it in a route definition
 * via the {@link ngRoute.$route $route} service. A common mistake is to declare the controller
 * again using `ng-controller` in the template itself.  This will cause the controller to be attached
 * and executed twice.
 *
 * @element ANY
 * @scope
 * @priority 500
 * @param {expression} ngController Name of a constructor function registered with the current
 * {@link ng.$controllerProvider $controllerProvider} or an {@link guide/expression expression}
 * that on the current scope evaluates to a constructor function.
 *
 * The controller instance can be published into a scope property by specifying
 * `ng-controller="as propertyName"`.
 *
 * If the current `$controllerProvider` is configured to use globals (via
 * {@link ng.$controllerProvider#allowGlobals `$controllerProvider.allowGlobals()` }), this may
 * also be the name of a globally accessible constructor function (deprecated, not recommended).
 *
 * @example
 * Here is a simple form for editing user contact information. Adding, removing, clearing, and
 * greeting are methods declared on the controller (see source tab). These methods can
 * easily be called from the angular markup. Any changes to the data are automatically reflected
 * in the View without the need for a manual update.
 *
 * Two different declaration styles are included below:
 *
 * * one binds methods and properties directly onto the controller using `this`:
 * `ng-controller="SettingsController1 as settings"`
 * * one injects `$scope` into the controller:
 * `ng-controller="SettingsController2"`
 *
 * The second option is more common in the Angular community, and is generally used in boilerplates
 * and in this guide. However, there are advantages to binding properties directly to the controller
 * and avoiding scope.
 *
 * * Using `controller as` makes it obvious which controller you are accessing in the template when
 * multiple controllers apply to an element.
 * * If you are writing your controllers as classes you have easier access to the properties and
 * methods, which will appear on the scope, from inside the controller code.
 * * Since there is always a `.` in the bindings, you don't have to worry about prototypal
 * inheritance masking primitives.
 *
 * This example demonstrates the `controller as` syntax.
 *
 * <example name="ngControllerAs" module="controllerAsExample">
 *   <file name="index.html">
 *    <div id="ctrl-as-exmpl" ng-controller="SettingsController1 as settings">
 *      <label>Name: <input type="text" ng-model="settings.name"/></label>
 *      <button ng-click="settings.greet()">greet</button><br/>
 *      Contact:
 *      <ul>
 *        <li ng-repeat="contact in settings.contacts">
 *          <select ng-model="contact.type" aria-label="Contact method" id="select_{{$index}}">
 *             <option>phone</option>
 *             <option>email</option>
 *          </select>
 *          <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *          <button ng-click="settings.clearContact(contact)">clear</button>
 *          <button ng-click="settings.removeContact(contact)" aria-label="Remove">X</button>
 *        </li>
 *        <li><button ng-click="settings.addContact()">add</button></li>
 *     </ul>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('controllerAsExample', [])
 *      .controller('SettingsController1', SettingsController1);
 *
 *    function SettingsController1() {
 *      this.name = 'John Smith';
 *      this.contacts = [
 *        {type: 'phone', value: '408 555 1212'},
 *        {type: 'email', value: 'john.smith@example.org'}
 *      ];
 *    }
 *
 *    SettingsController1.prototype.greet = function() {
 *      alert(this.name);
 *    };
 *
 *    SettingsController1.prototype.addContact = function() {
 *      this.contacts.push({type: 'email', value: 'yourname@example.org'});
 *    };
 *
 *    SettingsController1.prototype.removeContact = function(contactToRemove) {
 *     var index = this.contacts.indexOf(contactToRemove);
 *      this.contacts.splice(index, 1);
 *    };
 *
 *    SettingsController1.prototype.clearContact = function(contact) {
 *      contact.type = 'phone';
 *      contact.value = '';
 *    };
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should check controller as', function() {
 *       var container = element(by.id('ctrl-as-exmpl'));
 *         expect(container.element(by.model('settings.name'))
 *           .getAttribute('value')).toBe('John Smith');
 *
 *       var firstRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(0));
 *       var secondRepeat =
 *           container.element(by.repeater('contact in settings.contacts').row(1));
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('408 555 1212');
 *
 *       expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('john.smith@example.org');
 *
 *       firstRepeat.element(by.buttonText('clear')).click();
 *
 *       expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *           .toBe('');
 *
 *       container.element(by.buttonText('add')).click();
 *
 *       expect(container.element(by.repeater('contact in settings.contacts').row(2))
 *           .element(by.model('contact.value'))
 *           .getAttribute('value'))
 *           .toBe('yourname@example.org');
 *     });
 *   </file>
 * </example>
 *
 * This example demonstrates the "attach to `$scope`" style of controller.
 *
 * <example name="ngController" module="controllerExample">
 *  <file name="index.html">
 *   <div id="ctrl-exmpl" ng-controller="SettingsController2">
 *     <label>Name: <input type="text" ng-model="name"/></label>
 *     <button ng-click="greet()">greet</button><br/>
 *     Contact:
 *     <ul>
 *       <li ng-repeat="contact in contacts">
 *         <select ng-model="contact.type" id="select_{{$index}}">
 *            <option>phone</option>
 *            <option>email</option>
 *         </select>
 *         <input type="text" ng-model="contact.value" aria-labelledby="select_{{$index}}" />
 *         <button ng-click="clearContact(contact)">clear</button>
 *         <button ng-click="removeContact(contact)">X</button>
 *       </li>
 *       <li>[ <button ng-click="addContact()">add</button> ]</li>
 *    </ul>
 *   </div>
 *  </file>
 *  <file name="app.js">
 *   angular.module('controllerExample', [])
 *     .controller('SettingsController2', ['$scope', SettingsController2]);
 *
 *   function SettingsController2($scope) {
 *     $scope.name = 'John Smith';
 *     $scope.contacts = [
 *       {type:'phone', value:'408 555 1212'},
 *       {type:'email', value:'john.smith@example.org'}
 *     ];
 *
 *     $scope.greet = function() {
 *       alert($scope.name);
 *     };
 *
 *     $scope.addContact = function() {
 *       $scope.contacts.push({type:'email', value:'yourname@example.org'});
 *     };
 *
 *     $scope.removeContact = function(contactToRemove) {
 *       var index = $scope.contacts.indexOf(contactToRemove);
 *       $scope.contacts.splice(index, 1);
 *     };
 *
 *     $scope.clearContact = function(contact) {
 *       contact.type = 'phone';
 *       contact.value = '';
 *     };
 *   }
 *  </file>
 *  <file name="protractor.js" type="protractor">
 *    it('should check controller', function() {
 *      var container = element(by.id('ctrl-exmpl'));
 *
 *      expect(container.element(by.model('name'))
 *          .getAttribute('value')).toBe('John Smith');
 *
 *      var firstRepeat =
 *          container.element(by.repeater('contact in contacts').row(0));
 *      var secondRepeat =
 *          container.element(by.repeater('contact in contacts').row(1));
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('408 555 1212');
 *      expect(secondRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('john.smith@example.org');
 *
 *      firstRepeat.element(by.buttonText('clear')).click();
 *
 *      expect(firstRepeat.element(by.model('contact.value')).getAttribute('value'))
 *          .toBe('');
 *
 *      container.element(by.buttonText('add')).click();
 *
 *      expect(container.element(by.repeater('contact in contacts').row(2))
 *          .element(by.model('contact.value'))
 *          .getAttribute('value'))
 *          .toBe('yourname@example.org');
 *    });
 *  </file>
 *</example>

 */
var ngControllerDirective = [function() {
  return {
    restrict: 'A',
    scope: true,
    controller: '@',
    priority: 500
  };
}];

/**
 * @ngdoc directive
 * @name ngCsp
 *
 * @restrict A
 * @element ANY
 * @description
 *
 * Angular has some features that can conflict with certain restrictions that are applied when using
 * [CSP (Content Security Policy)](https://developer.mozilla.org/en/Security/CSP) rules.
 *
 * If you intend to implement CSP with these rules then you must tell Angular not to use these
 * features.
 *
 * This is necessary when developing things like Google Chrome Extensions or Universal Windows Apps.
 *
 *
 * The following default rules in CSP affect Angular:
 *
 * * The use of `eval()`, `Function(string)` and similar functions to dynamically create and execute
 * code from strings is forbidden. Angular makes use of this in the {@link $parse} service to
 * provide a 30% increase in the speed of evaluating Angular expressions. (This CSP rule can be
 * disabled with the CSP keyword `unsafe-eval`, but it is generally not recommended as it would
 * weaken the protections offered by CSP.)
 *
 * * The use of inline resources, such as inline `<script>` and `<style>` elements, are forbidden.
 * This prevents apps from injecting custom styles directly into the document. Angular makes use of
 * this to include some CSS rules (e.g. {@link ngCloak} and {@link ngHide}). To make these
 * directives work when a CSP rule is blocking inline styles, you must link to the `angular-csp.css`
 * in your HTML manually. (This CSP rule can be disabled with the CSP keyword `unsafe-inline`, but
 * it is generally not recommended as it would weaken the protections offered by CSP.)
 *
 * If you do not provide `ngCsp` then Angular tries to autodetect if CSP is blocking dynamic code
 * creation from strings (e.g., `unsafe-eval` not specified in CSP header) and automatically
 * deactivates this feature in the {@link $parse} service. This autodetection, however, triggers a
 * CSP error to be logged in the console:
 *
 * ```
 * Refused to evaluate a string as JavaScript because 'unsafe-eval' is not an allowed source of
 * script in the following Content Security Policy directive: "default-src 'self'". Note that
 * 'script-src' was not explicitly set, so 'default-src' is used as a fallback.
 * ```
 *
 * This error is harmless but annoying. To prevent the error from showing up, put the `ngCsp`
 * directive on an element of the HTML document that appears before the `<script>` tag that loads
 * the `angular.js` file.
 *
 * *Note: This directive is only available in the `ng-csp` and `data-ng-csp` attribute form.*
 *
 * You can specify which of the CSP related Angular features should be deactivated by providing
 * a value for the `ng-csp` attribute. The options are as follows:
 *
 * * no-inline-style: this stops Angular from injecting CSS styles into the DOM
 *
 * * no-unsafe-eval: this stops Angular from optimizing $parse with unsafe eval of strings
 *
 * You can use these values in the following combinations:
 *
 *
 * * No declaration means that Angular will assume that you can do inline styles, but it will do
 * a runtime check for unsafe-eval. E.g. `<body>`. This is backwardly compatible with previous
 * versions of Angular.
 *
 * * A simple `ng-csp` (or `data-ng-csp`) attribute will tell Angular to deactivate both inline
 * styles and unsafe eval. E.g. `<body ng-csp>`. This is backwardly compatible with previous
 * versions of Angular.
 *
 * * Specifying only `no-unsafe-eval` tells Angular that we must not use eval, but that we can
 * inject inline styles. E.g. `<body ng-csp="no-unsafe-eval">`.
 *
 * * Specifying only `no-inline-style` tells Angular that we must not inject styles, but that we can
 * run eval - no automatic check for unsafe eval will occur. E.g. `<body ng-csp="no-inline-style">`
 *
 * * Specifying both `no-unsafe-eval` and `no-inline-style` tells Angular that we must not inject
 * styles nor use eval, which is the same as an empty: ng-csp.
 * E.g.`<body ng-csp="no-inline-style;no-unsafe-eval">`
 *
 * @example
 * This example shows how to apply the `ngCsp` directive to the `html` tag.
   ```html
     <!doctype html>
     <html ng-app ng-csp>
     ...
     ...
     </html>
   ```
  * @example
      <!-- Note: the `.csp` suffix in the example name triggers CSP mode in our http server! -->
      <example name="example.csp" module="cspExample" ng-csp="true">
        <file name="index.html">
          <div ng-controller="MainController as ctrl">
            <div>
              <button ng-click="ctrl.inc()" id="inc">Increment</button>
              <span id="counter">
                {{ctrl.counter}}
              </span>
            </div>

            <div>
              <button ng-click="ctrl.evil()" id="evil">Evil</button>
              <span id="evilError">
                {{ctrl.evilError}}
              </span>
            </div>
          </div>
        </file>
        <file name="script.js">
           angular.module('cspExample', [])
             .controller('MainController', function MainController() {
                this.counter = 0;
                this.inc = function() {
                  this.counter++;
                };
                this.evil = function() {
                  try {
                    eval('1+2'); // eslint-disable-line no-eval
                  } catch (e) {
                    this.evilError = e.message;
                  }
                };
              });
        </file>
        <file name="protractor.js" type="protractor">
          var util, webdriver;

          var incBtn = element(by.id('inc'));
          var counter = element(by.id('counter'));
          var evilBtn = element(by.id('evil'));
          var evilError = element(by.id('evilError'));

          function getAndClearSevereErrors() {
            return browser.manage().logs().get('browser').then(function(browserLog) {
              return browserLog.filter(function(logEntry) {
                return logEntry.level.value > webdriver.logging.Level.WARNING.value;
              });
            });
          }

          function clearErrors() {
            getAndClearSevereErrors();
          }

          function expectNoErrors() {
            getAndClearSevereErrors().then(function(filteredLog) {
              expect(filteredLog.length).toEqual(0);
              if (filteredLog.length) {
                console.log('browser console errors: ' + util.inspect(filteredLog));
              }
            });
          }

          function expectError(regex) {
            getAndClearSevereErrors().then(function(filteredLog) {
              var found = false;
              filteredLog.forEach(function(log) {
                if (log.message.match(regex)) {
                  found = true;
                }
              });
              if (!found) {
                throw new Error('expected an error that matches ' + regex);
              }
            });
          }

          beforeEach(function() {
            util = require('util');
            webdriver = require('selenium-webdriver');
          });

          // For now, we only test on Chrome,
          // as Safari does not load the page with Protractor's injected scripts,
          // and Firefox webdriver always disables content security policy (#6358)
          if (browser.params.browser !== 'chrome') {
            return;
          }

          it('should not report errors when the page is loaded', function() {
            // clear errors so we are not dependent on previous tests
            clearErrors();
            // Need to reload the page as the page is already loaded when
            // we come here
            browser.driver.getCurrentUrl().then(function(url) {
              browser.get(url);
            });
            expectNoErrors();
          });

          it('should evaluate expressions', function() {
            expect(counter.getText()).toEqual('0');
            incBtn.click();
            expect(counter.getText()).toEqual('1');
            expectNoErrors();
          });

          it('should throw and report an error when using "eval"', function() {
            evilBtn.click();
            expect(evilError.getText()).toMatch(/Content Security Policy/);
            expectError(/Content Security Policy/);
          });
        </file>
      </example>
  */

// `ngCsp` is not implemented as a proper directive any more, because we need it be processed while
// we bootstrap the app (before `$parse` is instantiated). For this reason, we just have the `csp()`
// fn that looks for the `ng-csp` attribute anywhere in the current doc.

/**
 * @ngdoc directive
 * @name ngClick
 *
 * @description
 * The ngClick directive allows you to specify custom behavior when
 * an element is clicked.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngClick {@link guide/expression Expression} to evaluate upon
 * click. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-click">
     <file name="index.html">
      <button ng-click="count = count + 1" ng-init="count=0">
        Increment
      </button>
      <span>
        count: {{count}}
      </span>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-click', function() {
         expect(element(by.binding('count')).getText()).toMatch('0');
         element(by.css('button')).click();
         expect(element(by.binding('count')).getText()).toMatch('1');
       });
     </file>
   </example>
 */
/*
 * A collection of directives that allows creation of custom event handlers that are defined as
 * angular expressions and are compiled and executed within the current scope.
 */
var ngEventDirectives = {};

// For events that might fire synchronously during DOM manipulation
// we need to execute their event handlers asynchronously using $evalAsync,
// so that they are not executed in an inconsistent state.
var forceAsyncEvents = {
  'blur': true,
  'focus': true
};
forEach(
  'click dblclick mousedown mouseup mouseover mouseout mousemove mouseenter mouseleave keydown keyup keypress submit focus blur copy cut paste'.split(' '),
  function(eventName) {
    var directiveName = directiveNormalize('ng-' + eventName);
    ngEventDirectives[directiveName] = ['$parse', '$rootScope', function($parse, $rootScope) {
      return {
        restrict: 'A',
        compile: function($element, attr) {
          // NOTE:
          // We expose the powerful `$event` object on the scope that provides access to the Window,
          // etc. This is OK, because expressions are not sandboxed any more (and the expression
          // sandbox was never meant to be a security feature anyway).
          var fn = $parse(attr[directiveName]);
          return function ngEventHandler(scope, element) {
            element.on(eventName, function(event) {
              var callback = function() {
                fn(scope, {$event: event});
              };
              if (forceAsyncEvents[eventName] && $rootScope.$$phase) {
                scope.$evalAsync(callback);
              } else {
                scope.$apply(callback);
              }
            });
          };
        }
      };
    }];
  }
);

/**
 * @ngdoc directive
 * @name ngDblclick
 *
 * @description
 * The `ngDblclick` directive allows you to specify custom behavior on a dblclick event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngDblclick {@link guide/expression Expression} to evaluate upon
 * a dblclick. (The Event object is available as `$event`)
 *
 * @example
   <example name="ng-dblclick">
     <file name="index.html">
      <button ng-dblclick="count = count + 1" ng-init="count=0">
        Increment (on double click)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousedown
 *
 * @description
 * The ngMousedown directive allows you to specify custom behavior on mousedown event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMousedown {@link guide/expression Expression} to evaluate upon
 * mousedown. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousedown">
     <file name="index.html">
      <button ng-mousedown="count = count + 1" ng-init="count=0">
        Increment (on mouse down)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseup
 *
 * @description
 * Specify custom behavior on mouseup event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseup {@link guide/expression Expression} to evaluate upon
 * mouseup. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseup">
     <file name="index.html">
      <button ng-mouseup="count = count + 1" ng-init="count=0">
        Increment (on mouse up)
      </button>
      count: {{count}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngMouseover
 *
 * @description
 * Specify custom behavior on mouseover event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseover {@link guide/expression Expression} to evaluate upon
 * mouseover. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseover">
     <file name="index.html">
      <button ng-mouseover="count = count + 1" ng-init="count=0">
        Increment (when mouse is over)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseenter
 *
 * @description
 * Specify custom behavior on mouseenter event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseenter {@link guide/expression Expression} to evaluate upon
 * mouseenter. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseenter">
     <file name="index.html">
      <button ng-mouseenter="count = count + 1" ng-init="count=0">
        Increment (when mouse enters)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMouseleave
 *
 * @description
 * Specify custom behavior on mouseleave event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMouseleave {@link guide/expression Expression} to evaluate upon
 * mouseleave. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mouseleave">
     <file name="index.html">
      <button ng-mouseleave="count = count + 1" ng-init="count=0">
        Increment (when mouse leaves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngMousemove
 *
 * @description
 * Specify custom behavior on mousemove event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngMousemove {@link guide/expression Expression} to evaluate upon
 * mousemove. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-mousemove">
     <file name="index.html">
      <button ng-mousemove="count = count + 1" ng-init="count=0">
        Increment (when mouse moves)
      </button>
      count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeydown
 *
 * @description
 * Specify custom behavior on keydown event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngKeydown {@link guide/expression Expression} to evaluate upon
 * keydown. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keydown">
     <file name="index.html">
      <input ng-keydown="count = count + 1" ng-init="count=0">
      key down count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeyup
 *
 * @description
 * Specify custom behavior on keyup event.
 *
 * @element ANY
 * @priority 0
 * @param {expression} ngKeyup {@link guide/expression Expression} to evaluate upon
 * keyup. (Event object is available as `$event` and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keyup">
     <file name="index.html">
       <p>Typing in the input box below updates the key count</p>
       <input ng-keyup="count = count + 1" ng-init="count=0"> key up count: {{count}}

       <p>Typing in the input box below updates the keycode</p>
       <input ng-keyup="event=$event">
       <p>event keyCode: {{ event.keyCode }}</p>
       <p>event altKey: {{ event.altKey }}</p>
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngKeypress
 *
 * @description
 * Specify custom behavior on keypress event.
 *
 * @element ANY
 * @param {expression} ngKeypress {@link guide/expression Expression} to evaluate upon
 * keypress. ({@link guide/expression#-event- Event object is available as `$event`}
 * and can be interrogated for keyCode, altKey, etc.)
 *
 * @example
   <example name="ng-keypress">
     <file name="index.html">
      <input ng-keypress="count = count + 1" ng-init="count=0">
      key press count: {{count}}
     </file>
   </example>
 */


/**
 * @ngdoc directive
 * @name ngSubmit
 *
 * @description
 * Enables binding angular expressions to onsubmit events.
 *
 * Additionally it prevents the default action (which for form means sending the request to the
 * server and reloading the current page), but only if the form does not contain `action`,
 * `data-action`, or `x-action` attributes.
 *
 * <div class="alert alert-warning">
 * **Warning:** Be careful not to cause "double-submission" by using both the `ngClick` and
 * `ngSubmit` handlers together. See the
 * {@link form#submitting-a-form-and-preventing-the-default-action `form` directive documentation}
 * for a detailed discussion of when `ngSubmit` may be triggered.
 * </div>
 *
 * @element form
 * @priority 0
 * @param {expression} ngSubmit {@link guide/expression Expression} to eval.
 * ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example module="submitExample" name="ng-submit">
     <file name="index.html">
      <script>
        angular.module('submitExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.list = [];
            $scope.text = 'hello';
            $scope.submit = function() {
              if ($scope.text) {
                $scope.list.push(this.text);
                $scope.text = '';
              }
            };
          }]);
      </script>
      <form ng-submit="submit()" ng-controller="ExampleController">
        Enter text and hit enter:
        <input type="text" ng-model="text" name="text" />
        <input type="submit" id="submit" value="Submit" />
        <pre>list={{list}}</pre>
      </form>
     </file>
     <file name="protractor.js" type="protractor">
       it('should check ng-submit', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
         expect(element(by.model('text')).getAttribute('value')).toBe('');
       });
       it('should ignore empty strings', function() {
         expect(element(by.binding('list')).getText()).toBe('list=[]');
         element(by.css('#submit')).click();
         element(by.css('#submit')).click();
         expect(element(by.binding('list')).getText()).toContain('hello');
        });
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngFocus
 *
 * @description
 * Specify custom behavior on focus event.
 *
 * Note: As the `focus` event is executed synchronously when calling `input.focus()`
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngFocus {@link guide/expression Expression} to evaluate upon
 * focus. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngBlur
 *
 * @description
 * Specify custom behavior on blur event.
 *
 * A [blur event](https://developer.mozilla.org/en-US/docs/Web/Events/blur) fires when
 * an element has lost focus.
 *
 * Note: As the `blur` event is executed synchronously also during DOM manipulations
 * (e.g. removing a focussed input),
 * AngularJS executes the expression using `scope.$evalAsync` if the event is fired
 * during an `$apply` to ensure a consistent state.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngBlur {@link guide/expression Expression} to evaluate upon
 * blur. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
 * See {@link ng.directive:ngClick ngClick}
 */

/**
 * @ngdoc directive
 * @name ngCopy
 *
 * @description
 * Specify custom behavior on copy event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngCopy {@link guide/expression Expression} to evaluate upon
 * copy. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-copy">
     <file name="index.html">
      <input ng-copy="copied=true" ng-init="copied=false; value='copy me'" ng-model="value">
      copied: {{copied}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngCut
 *
 * @description
 * Specify custom behavior on cut event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngCut {@link guide/expression Expression} to evaluate upon
 * cut. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-cut">
     <file name="index.html">
      <input ng-cut="cut=true" ng-init="cut=false; value='cut me'" ng-model="value">
      cut: {{cut}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngPaste
 *
 * @description
 * Specify custom behavior on paste event.
 *
 * @element window, input, select, textarea, a
 * @priority 0
 * @param {expression} ngPaste {@link guide/expression Expression} to evaluate upon
 * paste. ({@link guide/expression#-event- Event object is available as `$event`})
 *
 * @example
   <example name="ng-paste">
     <file name="index.html">
      <input ng-paste="paste=true" ng-init="paste=false" placeholder='paste here'>
      pasted: {{paste}}
     </file>
   </example>
 */

/**
 * @ngdoc directive
 * @name ngIf
 * @restrict A
 * @multiElement
 *
 * @description
 * The `ngIf` directive removes or recreates a portion of the DOM tree based on an
 * {expression}. If the expression assigned to `ngIf` evaluates to a false
 * value then the element is removed from the DOM, otherwise a clone of the
 * element is reinserted into the DOM.
 *
 * `ngIf` differs from `ngShow` and `ngHide` in that `ngIf` completely removes and recreates the
 * element in the DOM rather than changing its visibility via the `display` css property.  A common
 * case when this difference is significant is when using css selectors that rely on an element's
 * position within the DOM, such as the `:first-child` or `:last-child` pseudo-classes.
 *
 * Note that when an element is removed using `ngIf` its scope is destroyed and a new scope
 * is created when the element is restored.  The scope created within `ngIf` inherits from
 * its parent scope using
 * [prototypal inheritance](https://github.com/angular/angular.js/wiki/Understanding-Scopes#javascript-prototypal-inheritance).
 * An important implication of this is if `ngModel` is used within `ngIf` to bind to
 * a javascript primitive defined in the parent scope. In this case any modifications made to the
 * variable within the child scope will override (hide) the value in the parent scope.
 *
 * Also, `ngIf` recreates elements using their compiled state. An example of this behavior
 * is if an element's class attribute is directly modified after it's compiled, using something like
 * jQuery's `.addClass()` method, and the element is later removed. When `ngIf` recreates the element
 * the added class will be lost because the original compiled state is used to regenerate the element.
 *
 * Additionally, you can provide animations via the `ngAnimate` module to animate the `enter`
 * and `leave` effects.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | just after the `ngIf` contents change and a new DOM element is created and injected into the `ngIf` container |
 * | {@link ng.$animate#leave leave}  | just before the `ngIf` contents are removed from the DOM |
 *
 * @element ANY
 * @scope
 * @priority 600
 * @param {expression} ngIf If the {@link guide/expression expression} is falsy then
 *     the element is removed from the DOM tree. If it is truthy a copy of the compiled
 *     element is added to the DOM tree.
 *
 * @example
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-if">
    <file name="index.html">
      <label>Click me: <input type="checkbox" ng-model="checked" ng-init="checked=true" /></label><br/>
      Show when checked:
      <span ng-if="checked" class="animate-if">
        This is removed when the checkbox is unchecked.
      </span>
    </file>
    <file name="animations.css">
      .animate-if {
        background:white;
        border:1px solid black;
        padding:10px;
      }

      .animate-if.ng-enter, .animate-if.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;
      }

      .animate-if.ng-enter,
      .animate-if.ng-leave.ng-leave-active {
        opacity:0;
      }

      .animate-if.ng-leave,
      .animate-if.ng-enter.ng-enter-active {
        opacity:1;
      }
    </file>
  </example>
 */
var ngIfDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    multiElement: true,
    transclude: 'element',
    priority: 600,
    terminal: true,
    restrict: 'A',
    $$tlb: true,
    link: function($scope, $element, $attr, ctrl, $transclude) {
        var block, childScope, previousElements;
        $scope.$watch($attr.ngIf, function ngIfWatchAction(value) {

          if (value) {
            if (!childScope) {
              $transclude(function(clone, newScope) {
                childScope = newScope;
                clone[clone.length++] = $compile.$$createComment('end ngIf', $attr.ngIf);
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block = {
                  clone: clone
                };
                $animate.enter(clone, $element.parent(), $element);
              });
            }
          } else {
            if (previousElements) {
              previousElements.remove();
              previousElements = null;
            }
            if (childScope) {
              childScope.$destroy();
              childScope = null;
            }
            if (block) {
              previousElements = getBlockNodes(block.clone);
              $animate.leave(previousElements).done(function(response) {
                if (response !== false) previousElements = null;
              });
              block = null;
            }
          }
        });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngInclude
 * @restrict ECA
 *
 * @description
 * Fetches, compiles and includes an external HTML fragment.
 *
 * By default, the template URL is restricted to the same domain and protocol as the
 * application document. This is done by calling {@link $sce#getTrustedResourceUrl
 * $sce.getTrustedResourceUrl} on it. To load templates from other domains or protocols
 * you may either {@link ng.$sceDelegateProvider#resourceUrlWhitelist whitelist them} or
 * {@link $sce#trustAsResourceUrl wrap them} as trusted values. Refer to Angular's {@link
 * ng.$sce Strict Contextual Escaping}.
 *
 * In addition, the browser's
 * [Same Origin Policy](https://code.google.com/p/browsersec/wiki/Part2#Same-origin_policy_for_XMLHttpRequest)
 * and [Cross-Origin Resource Sharing (CORS)](http://www.w3.org/TR/cors/)
 * policy may further restrict whether the template is successfully loaded.
 * For example, `ngInclude` won't work for cross-domain requests on all browsers and for `file://`
 * access on some browsers.
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the expression changes, on the new include |
 * | {@link ng.$animate#leave leave}  | when the expression changes, on the old include |
 *
 * The enter and leave animation occur concurrently.
 *
 * @scope
 * @priority 400
 *
 * @param {string} ngInclude|src angular expression evaluating to URL. If the source is a string constant,
 *                 make sure you wrap it in **single** quotes, e.g. `src="'myPartialTemplate.html'"`.
 * @param {string=} onload Expression to evaluate when a new partial is loaded.
 *                  <div class="alert alert-warning">
 *                  **Note:** When using onload on SVG elements in IE11, the browser will try to call
 *                  a function with the name on the window element, which will usually throw a
 *                  "function is undefined" error. To fix this, you can instead use `data-onload` or a
 *                  different form that {@link guide/directive#normalization matches} `onload`.
 *                  </div>
   *
 * @param {string=} autoscroll Whether `ngInclude` should call {@link ng.$anchorScroll
 *                  $anchorScroll} to scroll the viewport after the content is loaded.
 *
 *                  - If the attribute is not set, disable scrolling.
 *                  - If the attribute is set without value, enable scrolling.
 *                  - Otherwise enable scrolling only if the expression evaluates to truthy value.
 *
 * @example
  <example module="includeExample" deps="angular-animate.js" animations="true" name="ng-include">
    <file name="index.html">
     <div ng-controller="ExampleController">
       <select ng-model="template" ng-options="t.name for t in templates">
        <option value="">(blank)</option>
       </select>
       url of the template: <code>{{template.url}}</code>
       <hr/>
       <div class="slide-animate-container">
         <div class="slide-animate" ng-include="template.url"></div>
       </div>
     </div>
    </file>
    <file name="script.js">
      angular.module('includeExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.templates =
            [{ name: 'template1.html', url: 'template1.html'},
             { name: 'template2.html', url: 'template2.html'}];
          $scope.template = $scope.templates[0];
        }]);
     </file>
    <file name="template1.html">
      Content of template1.html
    </file>
    <file name="template2.html">
      Content of template2.html
    </file>
    <file name="animations.css">
      .slide-animate-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .slide-animate {
        padding:10px;
      }

      .slide-animate.ng-enter, .slide-animate.ng-leave {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
        display:block;
        padding:10px;
      }

      .slide-animate.ng-enter {
        top:-50px;
      }
      .slide-animate.ng-enter.ng-enter-active {
        top:0;
      }

      .slide-animate.ng-leave {
        top:0;
      }
      .slide-animate.ng-leave.ng-leave-active {
        top:50px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var templateSelect = element(by.model('template'));
      var includeElem = element(by.css('[ng-include]'));

      it('should load template1.html', function() {
        expect(includeElem.getText()).toMatch(/Content of template1.html/);
      });

      it('should load template2.html', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          // See https://github.com/angular/protractor/issues/480
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(2).click();
        expect(includeElem.getText()).toMatch(/Content of template2.html/);
      });

      it('should change to blank', function() {
        if (browser.params.browser === 'firefox') {
          // Firefox can't handle using selects
          return;
        }
        templateSelect.click();
        templateSelect.all(by.css('option')).get(0).click();
        expect(includeElem.isPresent()).toBe(false);
      });
    </file>
  </example>
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentRequested
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted every time the ngInclude content is requested.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentLoaded
 * @eventType emit on the current ngInclude scope
 * @description
 * Emitted every time the ngInclude content is reloaded.
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */


/**
 * @ngdoc event
 * @name ngInclude#$includeContentError
 * @eventType emit on the scope ngInclude was declared in
 * @description
 * Emitted when a template HTTP request yields an erroneous response (status < 200 || status > 299)
 *
 * @param {Object} angularEvent Synthetic event object.
 * @param {String} src URL of content to load.
 */
var ngIncludeDirective = ['$templateRequest', '$anchorScroll', '$animate',
                  function($templateRequest,   $anchorScroll,   $animate) {
  return {
    restrict: 'ECA',
    priority: 400,
    terminal: true,
    transclude: 'element',
    controller: angular.noop,
    compile: function(element, attr) {
      var srcExp = attr.ngInclude || attr.src,
          onloadExp = attr.onload || '',
          autoScrollExp = attr.autoscroll;

      return function(scope, $element, $attr, ctrl, $transclude) {
        var changeCounter = 0,
            currentScope,
            previousElement,
            currentElement;

        var cleanupLastIncludeContent = function() {
          if (previousElement) {
            previousElement.remove();
            previousElement = null;
          }
          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }
          if (currentElement) {
            $animate.leave(currentElement).done(function(response) {
              if (response !== false) previousElement = null;
            });
            previousElement = currentElement;
            currentElement = null;
          }
        };

        scope.$watch(srcExp, function ngIncludeWatchAction(src) {
          var afterAnimation = function(response) {
            if (response !== false && isDefined(autoScrollExp) &&
              (!autoScrollExp || scope.$eval(autoScrollExp))) {
                $anchorScroll();
            }
          };
          var thisChangeId = ++changeCounter;

          if (src) {
            //set the 2nd param to true to ignore the template request error so that the inner
            //contents and scope can be cleaned up.
            $templateRequest(src, true).then(function(response) {
              if (scope.$$destroyed) return;

              if (thisChangeId !== changeCounter) return;
              var newScope = scope.$new();
              ctrl.template = response;

              // Note: This will also link all children of ng-include that were contained in the original
              // html. If that content contains controllers, ... they could pollute/change the scope.
              // However, using ng-include on an element with additional content does not make sense...
              // Note: We can't remove them in the cloneAttchFn of $transclude as that
              // function is called before linking the content, which would apply child
              // directives to non existing elements.
              var clone = $transclude(newScope, function(clone) {
                cleanupLastIncludeContent();
                $animate.enter(clone, null, $element).done(afterAnimation);
              });

              currentScope = newScope;
              currentElement = clone;

              currentScope.$emit('$includeContentLoaded', src);
              scope.$eval(onloadExp);
            }, function() {
              if (scope.$$destroyed) return;

              if (thisChangeId === changeCounter) {
                cleanupLastIncludeContent();
                scope.$emit('$includeContentError', src);
              }
            });
            scope.$emit('$includeContentRequested', src);
          } else {
            cleanupLastIncludeContent();
            ctrl.template = null;
          }
        });
      };
    }
  };
}];

// This directive is called during the $transclude call of the first `ngInclude` directive.
// It will replace and compile the content of the element with the loaded template.
// We need this directive so that the element content is already filled when
// the link function of another directive on the same element as ngInclude
// is called.
var ngIncludeFillContentDirective = ['$compile',
  function($compile) {
    return {
      restrict: 'ECA',
      priority: -400,
      require: 'ngInclude',
      link: function(scope, $element, $attr, ctrl) {
        if (toString.call($element[0]).match(/SVG/)) {
          // WebKit: https://bugs.webkit.org/show_bug.cgi?id=135698 --- SVG elements do not
          // support innerHTML, so detect this here and try to generate the contents
          // specially.
          $element.empty();
          $compile(jqLiteBuildFragment(ctrl.template, window.document).childNodes)(scope,
              function namespaceAdaptedClone(clone) {
            $element.append(clone);
          }, {futureParentElement: $element});
          return;
        }

        $element.html(ctrl.template);
        $compile($element.contents())(scope);
      }
    };
  }];

/**
 * @ngdoc directive
 * @name ngInit
 * @restrict AC
 *
 * @description
 * The `ngInit` directive allows you to evaluate an expression in the
 * current scope.
 *
 * <div class="alert alert-danger">
 * This directive can be abused to add unnecessary amounts of logic into your templates.
 * There are only a few appropriate uses of `ngInit`, such as for aliasing special properties of
 * {@link ng.directive:ngRepeat `ngRepeat`}, as seen in the demo below; and for injecting data via
 * server side scripting. Besides these few cases, you should use {@link guide/controller controllers}
 * rather than `ngInit` to initialize values on a scope.
 * </div>
 *
 * <div class="alert alert-warning">
 * **Note**: If you have assignment in `ngInit` along with a {@link ng.$filter `filter`}, make
 * sure you have parentheses to ensure correct operator precedence:
 * <pre class="prettyprint">
 * `<div ng-init="test1 = ($index | toString)"></div>`
 * </pre>
 * </div>
 *
 * @priority 450
 *
 * @element ANY
 * @param {expression} ngInit {@link guide/expression Expression} to eval.
 *
 * @example
   <example module="initExample" name="ng-init">
     <file name="index.html">
   <script>
     angular.module('initExample', [])
       .controller('ExampleController', ['$scope', function($scope) {
         $scope.list = [['a', 'b'], ['c', 'd']];
       }]);
   </script>
   <div ng-controller="ExampleController">
     <div ng-repeat="innerList in list" ng-init="outerIndex = $index">
       <div ng-repeat="value in innerList" ng-init="innerIndex = $index">
          <span class="example-init">list[ {{outerIndex}} ][ {{innerIndex}} ] = {{value}};</span>
       </div>
     </div>
   </div>
     </file>
     <file name="protractor.js" type="protractor">
       it('should alias index positions', function() {
         var elements = element.all(by.css('.example-init'));
         expect(elements.get(0).getText()).toBe('list[ 0 ][ 0 ] = a;');
         expect(elements.get(1).getText()).toBe('list[ 0 ][ 1 ] = b;');
         expect(elements.get(2).getText()).toBe('list[ 1 ][ 0 ] = c;');
         expect(elements.get(3).getText()).toBe('list[ 1 ][ 1 ] = d;');
       });
     </file>
   </example>
 */
var ngInitDirective = ngDirective({
  priority: 450,
  compile: function() {
    return {
      pre: function(scope, element, attrs) {
        scope.$eval(attrs.ngInit);
      }
    };
  }
});

/**
 * @ngdoc directive
 * @name ngList
 *
 * @description
 * Text input that converts between a delimited string and an array of strings. The default
 * delimiter is a comma followed by a space - equivalent to `ng-list=", "`. You can specify a custom
 * delimiter as the value of the `ngList` attribute - for example, `ng-list=" | "`.
 *
 * The behaviour of the directive is affected by the use of the `ngTrim` attribute.
 * * If `ngTrim` is set to `"false"` then whitespace around both the separator and each
 *   list item is respected. This implies that the user of the directive is responsible for
 *   dealing with whitespace but also allows you to use whitespace as a delimiter, such as a
 *   tab or newline character.
 * * Otherwise whitespace around the delimiter is ignored when splitting (although it is respected
 *   when joining the list items back together) and whitespace around each list item is stripped
 *   before it is added to the model.
 *
 * ### Example with Validation
 *
 * <example name="ngList-directive" module="listExample">
 *   <file name="app.js">
 *      angular.module('listExample', [])
 *        .controller('ExampleController', ['$scope', function($scope) {
 *          $scope.names = ['morpheus', 'neo', 'trinity'];
 *        }]);
 *   </file>
 *   <file name="index.html">
 *    <form name="myForm" ng-controller="ExampleController">
 *      <label>List: <input name="namesInput" ng-model="names" ng-list required></label>
 *      <span role="alert">
 *        <span class="error" ng-show="myForm.namesInput.$error.required">
 *        Required!</span>
 *      </span>
 *      <br>
 *      <tt>names = {{names}}</tt><br/>
 *      <tt>myForm.namesInput.$valid = {{myForm.namesInput.$valid}}</tt><br/>
 *      <tt>myForm.namesInput.$error = {{myForm.namesInput.$error}}</tt><br/>
 *      <tt>myForm.$valid = {{myForm.$valid}}</tt><br/>
 *      <tt>myForm.$error.required = {{!!myForm.$error.required}}</tt><br/>
 *     </form>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var listInput = element(by.model('names'));
 *     var names = element(by.exactBinding('names'));
 *     var valid = element(by.binding('myForm.namesInput.$valid'));
 *     var error = element(by.css('span.error'));
 *
 *     it('should initialize to model', function() {
 *       expect(names.getText()).toContain('["morpheus","neo","trinity"]');
 *       expect(valid.getText()).toContain('true');
 *       expect(error.getCssValue('display')).toBe('none');
 *     });
 *
 *     it('should be invalid if empty', function() {
 *       listInput.clear();
 *       listInput.sendKeys('');
 *
 *       expect(names.getText()).toContain('');
 *       expect(valid.getText()).toContain('false');
 *       expect(error.getCssValue('display')).not.toBe('none');
 *     });
 *   </file>
 * </example>
 *
 * ### Example - splitting on newline
 * <example name="ngList-directive-newlines">
 *   <file name="index.html">
 *    <textarea ng-model="list" ng-list="&#10;" ng-trim="false"></textarea>
 *    <pre>{{ list | json }}</pre>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it("should split the text by newlines", function() {
 *       var listInput = element(by.model('list'));
 *       var output = element(by.binding('list | json'));
 *       listInput.sendKeys('abc\ndef\nghi');
 *       expect(output.getText()).toContain('[\n  "abc",\n  "def",\n  "ghi"\n]');
 *     });
 *   </file>
 * </example>
 *
 * @element input
 * @param {string=} ngList optional delimiter that should be used to split the value.
 */
var ngListDirective = function() {
  return {
    restrict: 'A',
    priority: 100,
    require: 'ngModel',
    link: function(scope, element, attr, ctrl) {
      var ngList = attr.ngList || ', ';
      var trimValues = attr.ngTrim !== 'false';
      var separator = trimValues ? trim(ngList) : ngList;

      var parse = function(viewValue) {
        // If the viewValue is invalid (say required but empty) it will be `undefined`
        if (isUndefined(viewValue)) return;

        var list = [];

        if (viewValue) {
          forEach(viewValue.split(separator), function(value) {
            if (value) list.push(trimValues ? trim(value) : value);
          });
        }

        return list;
      };

      ctrl.$parsers.push(parse);
      ctrl.$formatters.push(function(value) {
        if (isArray(value)) {
          return value.join(ngList);
        }

        return undefined;
      });

      // Override the standard $isEmpty because an empty array means the input is empty.
      ctrl.$isEmpty = function(value) {
        return !value || !value.length;
      };
    }
  };
};

/* global VALID_CLASS: true,
  INVALID_CLASS: true,
  PRISTINE_CLASS: true,
  DIRTY_CLASS: true,
  UNTOUCHED_CLASS: true,
  TOUCHED_CLASS: true,
  PENDING_CLASS: true,
  addSetValidityMethod: true,
  setupValidity: true,
  defaultModelOptions: false
*/


var VALID_CLASS = 'ng-valid',
    INVALID_CLASS = 'ng-invalid',
    PRISTINE_CLASS = 'ng-pristine',
    DIRTY_CLASS = 'ng-dirty',
    UNTOUCHED_CLASS = 'ng-untouched',
    TOUCHED_CLASS = 'ng-touched',
    EMPTY_CLASS = 'ng-empty',
    NOT_EMPTY_CLASS = 'ng-not-empty';

var ngModelMinErr = minErr('ngModel');

/**
 * @ngdoc type
 * @name ngModel.NgModelController
 *
 * @property {*} $viewValue The actual value from the control's view. For `input` elements, this is a
 * String. See {@link ngModel.NgModelController#$setViewValue} for information about when the $viewValue
 * is set.
 *
 * @property {*} $modelValue The value in the model that the control is bound to.
 *
 * @property {Array.<Function>} $parsers Array of functions to execute, as a pipeline, whenever
 *  the control updates the ngModelController with a new {@link ngModel.NgModelController#$viewValue
    `$viewValue`} from the DOM, usually via user input.
    See {@link ngModel.NgModelController#$setViewValue `$setViewValue()`} for a detailed lifecycle explanation.
    Note that the `$parsers` are not called when the bound ngModel expression changes programmatically.

  The functions are called in array order, each passing
    its return value through to the next. The last return value is forwarded to the
    {@link ngModel.NgModelController#$validators `$validators`} collection.

  Parsers are used to sanitize / convert the {@link ngModel.NgModelController#$viewValue
    `$viewValue`}.

  Returning `undefined` from a parser means a parse error occurred. In that case,
    no {@link ngModel.NgModelController#$validators `$validators`} will run and the `ngModel`
    will be set to `undefined` unless {@link ngModelOptions `ngModelOptions.allowInvalid`}
    is set to `true`. The parse error is stored in `ngModel.$error.parse`.

  This simple example shows a parser that would convert text input value to lowercase:
 * ```js
 * function parse(value) {
 *   if (value) {
 *     return value.toLowerCase();
 *   }
 * }
 * ngModelController.$parsers.push(parse);
 * ```

 *
 * @property {Array.<Function>} $formatters Array of functions to execute, as a pipeline, whenever
    the bound ngModel expression changes programmatically. The `$formatters` are not called when the
    value of the control is changed by user interaction.

  Formatters are used to format / convert the {@link ngModel.NgModelController#$modelValue
    `$modelValue`} for display in the control.

  The functions are called in reverse array order, each passing the value through to the
    next. The last return value is used as the actual DOM value.

  This simple example shows a formatter that would convert the model value to uppercase:

 * ```js
 * function format(value) {
 *   if (value) {
 *     return value.toUpperCase();
 *   }
 * }
 * ngModel.$formatters.push(format);
 * ```
 *
 * @property {Object.<string, function>} $validators A collection of validators that are applied
 *      whenever the model value changes. The key value within the object refers to the name of the
 *      validator while the function refers to the validation operation. The validation operation is
 *      provided with the model value as an argument and must return a true or false value depending
 *      on the response of that validation.
 *
 * ```js
 * ngModel.$validators.validCharacters = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *   return /[0-9]+/.test(value) &&
 *          /[a-z]+/.test(value) &&
 *          /[A-Z]+/.test(value) &&
 *          /\W+/.test(value);
 * };
 * ```
 *
 * @property {Object.<string, function>} $asyncValidators A collection of validations that are expected to
 *      perform an asynchronous validation (e.g. a HTTP request). The validation function that is provided
 *      is expected to return a promise when it is run during the model validation process. Once the promise
 *      is delivered then the validation status will be set to true when fulfilled and false when rejected.
 *      When the asynchronous validators are triggered, each of the validators will run in parallel and the model
 *      value will only be updated once all validators have been fulfilled. As long as an asynchronous validator
 *      is unfulfilled, its key will be added to the controllers `$pending` property. Also, all asynchronous validators
 *      will only run once all synchronous validators have passed.
 *
 * Please note that if $http is used then it is important that the server returns a success HTTP response code
 * in order to fulfill the validation and a status level of `4xx` in order to reject the validation.
 *
 * ```js
 * ngModel.$asyncValidators.uniqueUsername = function(modelValue, viewValue) {
 *   var value = modelValue || viewValue;
 *
 *   // Lookup user by username
 *   return $http.get('/api/users/' + value).
 *      then(function resolved() {
 *        //username exists, this means validation fails
 *        return $q.reject('exists');
 *      }, function rejected() {
 *        //username does not exist, therefore this validation passes
 *        return true;
 *      });
 * };
 * ```
 *
 * @property {Array.<Function>} $viewChangeListeners Array of functions to execute whenever the
 *     view value has changed. It is called with no arguments, and its return value is ignored.
 *     This can be used in place of additional $watches against the model value.
 *
 * @property {Object} $error An object hash with all failing validator ids as keys.
 * @property {Object} $pending An object hash with all pending validator ids as keys.
 *
 * @property {boolean} $untouched True if control has not lost focus yet.
 * @property {boolean} $touched True if control has lost focus.
 * @property {boolean} $pristine True if user has not interacted with the control yet.
 * @property {boolean} $dirty True if user has already interacted with the control.
 * @property {boolean} $valid True if there is no error.
 * @property {boolean} $invalid True if at least one error on the control.
 * @property {string} $name The name attribute of the control.
 *
 * @description
 *
 * `NgModelController` provides API for the {@link ngModel `ngModel`} directive.
 * The controller contains services for data-binding, validation, CSS updates, and value formatting
 * and parsing. It purposefully does not contain any logic which deals with DOM rendering or
 * listening to DOM events.
 * Such DOM related logic should be provided by other directives which make use of
 * `NgModelController` for data-binding to control elements.
 * Angular provides this DOM logic for most {@link input `input`} elements.
 * At the end of this page you can find a {@link ngModel.NgModelController#custom-control-example
 * custom control example} that uses `ngModelController` to bind to `contenteditable` elements.
 *
 * @example
 * ### Custom Control Example
 * This example shows how to use `NgModelController` with a custom control to achieve
 * data-binding. Notice how different directives (`contenteditable`, `ng-model`, and `required`)
 * collaborate together to achieve the desired result.
 *
 * `contenteditable` is an HTML5 attribute, which tells the browser to let the element
 * contents be edited in place by the user.
 *
 * We are using the {@link ng.service:$sce $sce} service here and include the {@link ngSanitize $sanitize}
 * module to automatically remove "bad" content like inline event listener (e.g. `<span onclick="...">`).
 * However, as we are using `$sce` the model can still decide to provide unsafe content if it marks
 * that content using the `$sce` service.
 *
 * <example name="NgModelController" module="customControl" deps="angular-sanitize.js">
    <file name="style.css">
      [contenteditable] {
        border: 1px solid black;
        background-color: white;
        min-height: 20px;
      }

      .ng-invalid {
        border: 1px solid red;
      }

    </file>
    <file name="script.js">
      angular.module('customControl', ['ngSanitize']).
        directive('contenteditable', ['$sce', function($sce) {
          return {
            restrict: 'A', // only activate on element attribute
            require: '?ngModel', // get a hold of NgModelController
            link: function(scope, element, attrs, ngModel) {
              if (!ngModel) return; // do nothing if no ng-model

              // Specify how UI should be updated
              ngModel.$render = function() {
                element.html($sce.getTrustedHtml(ngModel.$viewValue || ''));
              };

              // Listen for change events to enable binding
              element.on('blur keyup change', function() {
                scope.$evalAsync(read);
              });
              read(); // initialize

              // Write data to the model
              function read() {
                var html = element.html();
                // When we clear the content editable the browser leaves a <br> behind
                // If strip-br attribute is provided then we strip this out
                if (attrs.stripBr && html === '<br>') {
                  html = '';
                }
                ngModel.$setViewValue(html);
              }
            }
          };
        }]);
    </file>
    <file name="index.html">
      <form name="myForm">
       <div contenteditable
            name="myWidget" ng-model="userContent"
            strip-br="true"
            required>Change me!</div>
        <span ng-show="myForm.myWidget.$error.required">Required!</span>
       <hr>
       <textarea ng-model="userContent" aria-label="Dynamic textarea"></textarea>
      </form>
    </file>
    <file name="protractor.js" type="protractor">
    it('should data-bind and become invalid', function() {
      if (browser.params.browser === 'safari' || browser.params.browser === 'firefox') {
        // SafariDriver can't handle contenteditable
        // and Firefox driver can't clear contenteditables very well
        return;
      }
      var contentEditable = element(by.css('[contenteditable]'));
      var content = 'Change me!';

      expect(contentEditable.getText()).toEqual(content);

      contentEditable.clear();
      contentEditable.sendKeys(protractor.Key.BACK_SPACE);
      expect(contentEditable.getText()).toEqual('');
      expect(contentEditable.getAttribute('class')).toMatch(/ng-invalid-required/);
    });
    </file>
 * </example>
 *
 *
 */
NgModelController.$inject = ['$scope', '$exceptionHandler', '$attrs', '$element', '$parse', '$animate', '$timeout', '$q', '$interpolate'];
function NgModelController($scope, $exceptionHandler, $attr, $element, $parse, $animate, $timeout, $q, $interpolate) {
  this.$viewValue = Number.NaN;
  this.$modelValue = Number.NaN;
  this.$$rawModelValue = undefined; // stores the parsed modelValue / model set from scope regardless of validity.
  this.$validators = {};
  this.$asyncValidators = {};
  this.$parsers = [];
  this.$formatters = [];
  this.$viewChangeListeners = [];
  this.$untouched = true;
  this.$touched = false;
  this.$pristine = true;
  this.$dirty = false;
  this.$valid = true;
  this.$invalid = false;
  this.$error = {}; // keep invalid keys here
  this.$$success = {}; // keep valid keys here
  this.$pending = undefined; // keep pending keys here
  this.$name = $interpolate($attr.name || '', false)($scope);
  this.$$parentForm = nullFormCtrl;
  this.$options = defaultModelOptions;

  this.$$parsedNgModel = $parse($attr.ngModel);
  this.$$parsedNgModelAssign = this.$$parsedNgModel.assign;
  this.$$ngModelGet = this.$$parsedNgModel;
  this.$$ngModelSet = this.$$parsedNgModelAssign;
  this.$$pendingDebounce = null;
  this.$$parserValid = undefined;

  this.$$currentValidationRunId = 0;

  // https://github.com/angular/angular.js/issues/15833
  // Prevent `$$scope` from being iterated over by `copy` when NgModelController is deep watched
  Object.defineProperty(this, '$$scope', {value: $scope});
  this.$$attr = $attr;
  this.$$element = $element;
  this.$$animate = $animate;
  this.$$timeout = $timeout;
  this.$$parse = $parse;
  this.$$q = $q;
  this.$$exceptionHandler = $exceptionHandler;

  setupValidity(this);
  setupModelWatcher(this);
}

NgModelController.prototype = {
  $$initGetterSetters: function() {
    if (this.$options.getOption('getterSetter')) {
      var invokeModelGetter = this.$$parse(this.$$attr.ngModel + '()'),
          invokeModelSetter = this.$$parse(this.$$attr.ngModel + '($$$p)');

      this.$$ngModelGet = function($scope) {
        var modelValue = this.$$parsedNgModel($scope);
        if (isFunction(modelValue)) {
          modelValue = invokeModelGetter($scope);
        }
        return modelValue;
      };
      this.$$ngModelSet = function($scope, newValue) {
        if (isFunction(this.$$parsedNgModel($scope))) {
          invokeModelSetter($scope, {$$$p: newValue});
        } else {
          this.$$parsedNgModelAssign($scope, newValue);
        }
      };
    } else if (!this.$$parsedNgModel.assign) {
      throw ngModelMinErr('nonassign', 'Expression \'{0}\' is non-assignable. Element: {1}',
          this.$$attr.ngModel, startingTag(this.$$element));
    }
  },


  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$render
   *
   * @description
   * Called when the view needs to be updated. It is expected that the user of the ng-model
   * directive will implement this method.
   *
   * The `$render()` method is invoked in the following situations:
   *
   * * `$rollbackViewValue()` is called.  If we are rolling back the view value to the last
   *   committed value then `$render()` is called to update the input control.
   * * The value referenced by `ng-model` is changed programmatically and both the `$modelValue` and
   *   the `$viewValue` are different from last time.
   *
   * Since `ng-model` does not do a deep watch, `$render()` is only invoked if the values of
   * `$modelValue` and `$viewValue` are actually different from their previous values. If `$modelValue`
   * or `$viewValue` are objects (rather than a string or number) then `$render()` will not be
   * invoked if you only change a property on the objects.
   */
  $render: noop,

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$isEmpty
   *
   * @description
   * This is called when we need to determine if the value of an input is empty.
   *
   * For instance, the required directive does this to work out if the input has data or not.
   *
   * The default `$isEmpty` function checks whether the value is `undefined`, `''`, `null` or `NaN`.
   *
   * You can override this for input directives whose concept of being empty is different from the
   * default. The `checkboxInputType` directive does this because in its case a value of `false`
   * implies empty.
   *
   * @param {*} value The value of the input to check for emptiness.
   * @returns {boolean} True if `value` is "empty".
   */
  $isEmpty: function(value) {
    // eslint-disable-next-line no-self-compare
    return isUndefined(value) || value === '' || value === null || value !== value;
  },

  $$updateEmptyClasses: function(value) {
    if (this.$isEmpty(value)) {
      this.$$animate.removeClass(this.$$element, NOT_EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, EMPTY_CLASS);
    } else {
      this.$$animate.removeClass(this.$$element, EMPTY_CLASS);
      this.$$animate.addClass(this.$$element, NOT_EMPTY_CLASS);
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setPristine
   *
   * @description
   * Sets the control to its pristine state.
   *
   * This method can be called to remove the `ng-dirty` class and set the control to its pristine
   * state (`ng-pristine` class). A model is considered to be pristine when the control
   * has not been changed from when first compiled.
   */
  $setPristine: function() {
    this.$dirty = false;
    this.$pristine = true;
    this.$$animate.removeClass(this.$$element, DIRTY_CLASS);
    this.$$animate.addClass(this.$$element, PRISTINE_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setDirty
   *
   * @description
   * Sets the control to its dirty state.
   *
   * This method can be called to remove the `ng-pristine` class and set the control to its dirty
   * state (`ng-dirty` class). A model is considered to be dirty when the control has been changed
   * from when first compiled.
   */
  $setDirty: function() {
    this.$dirty = true;
    this.$pristine = false;
    this.$$animate.removeClass(this.$$element, PRISTINE_CLASS);
    this.$$animate.addClass(this.$$element, DIRTY_CLASS);
    this.$$parentForm.$setDirty();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setUntouched
   *
   * @description
   * Sets the control to its untouched state.
   *
   * This method can be called to remove the `ng-touched` class and set the control to its
   * untouched state (`ng-untouched` class). Upon compilation, a model is set as untouched
   * by default, however this function can be used to restore that state if the model has
   * already been touched by the user.
   */
  $setUntouched: function() {
    this.$touched = false;
    this.$untouched = true;
    this.$$animate.setClass(this.$$element, UNTOUCHED_CLASS, TOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setTouched
   *
   * @description
   * Sets the control to its touched state.
   *
   * This method can be called to remove the `ng-untouched` class and set the control to its
   * touched state (`ng-touched` class). A model is considered to be touched when the user has
   * first focused the control element and then shifted focus away from the control (blur event).
   */
  $setTouched: function() {
    this.$touched = true;
    this.$untouched = false;
    this.$$animate.setClass(this.$$element, TOUCHED_CLASS, UNTOUCHED_CLASS);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$rollbackViewValue
   *
   * @description
   * Cancel an update and reset the input element's value to prevent an update to the `$modelValue`,
   * which may be caused by a pending debounced event or because the input is waiting for some
   * future event.
   *
   * If you have an input that uses `ng-model-options` to set up debounced updates or updates that
   * depend on special events such as `blur`, there can be a period when the `$viewValue` is out of
   * sync with the ngModel's `$modelValue`.
   *
   * In this case, you can use `$rollbackViewValue()` to manually cancel the debounced / future update
   * and reset the input to the last committed view value.
   *
   * It is also possible that you run into difficulties if you try to update the ngModel's `$modelValue`
   * programmatically before these debounced/future events have resolved/occurred, because Angular's
   * dirty checking mechanism is not able to tell whether the model has actually changed or not.
   *
   * The `$rollbackViewValue()` method should be called before programmatically changing the model of an
   * input which may have such events pending. This is important in order to make sure that the
   * input field will be updated with the new model value and any pending operations are cancelled.
   *
   * <example name="ng-model-cancel-update" module="cancel-update-example">
   *   <file name="app.js">
   *     angular.module('cancel-update-example', [])
   *
   *     .controller('CancelUpdateController', ['$scope', function($scope) {
   *       $scope.model = {value1: '', value2: ''};
   *
   *       $scope.setEmpty = function(e, value, rollback) {
   *         if (e.keyCode === 27) {
   *           e.preventDefault();
   *           if (rollback) {
   *             $scope.myForm[value].$rollbackViewValue();
   *           }
   *           $scope.model[value] = '';
   *         }
   *       };
   *     }]);
   *   </file>
   *   <file name="index.html">
   *     <div ng-controller="CancelUpdateController">
   *       <p>Both of these inputs are only updated if they are blurred. Hitting escape should
   *       empty them. Follow these steps and observe the difference:</p>
   *       <ol>
   *         <li>Type something in the input. You will see that the model is not yet updated</li>
   *         <li>Press the Escape key.
   *           <ol>
   *             <li> In the first example, nothing happens, because the model is already '', and no
   *             update is detected. If you blur the input, the model will be set to the current view.
   *             </li>
   *             <li> In the second example, the pending update is cancelled, and the input is set back
   *             to the last committed view value (''). Blurring the input does nothing.
   *             </li>
   *           </ol>
   *         </li>
   *       </ol>
   *
   *       <form name="myForm" ng-model-options="{ updateOn: 'blur' }">
   *         <div>
   *           <p id="inputDescription1">Without $rollbackViewValue():</p>
   *           <input name="value1" aria-describedby="inputDescription1" ng-model="model.value1"
   *                  ng-keydown="setEmpty($event, 'value1')">
   *           value1: "{{ model.value1 }}"
   *         </div>
   *
   *         <div>
   *           <p id="inputDescription2">With $rollbackViewValue():</p>
   *           <input name="value2" aria-describedby="inputDescription2" ng-model="model.value2"
   *                  ng-keydown="setEmpty($event, 'value2', true)">
   *           value2: "{{ model.value2 }}"
   *         </div>
   *       </form>
   *     </div>
   *   </file>
       <file name="style.css">
          div {
            display: table-cell;
          }
          div:nth-child(1) {
            padding-right: 30px;
          }

        </file>
   * </example>
   */
  $rollbackViewValue: function() {
    this.$$timeout.cancel(this.$$pendingDebounce);
    this.$viewValue = this.$$lastCommittedViewValue;
    this.$render();
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$validate
   *
   * @description
   * Runs each of the registered validators (first synchronous validators and then
   * asynchronous validators).
   * If the validity changes to invalid, the model will be set to `undefined`,
   * unless {@link ngModelOptions `ngModelOptions.allowInvalid`} is `true`.
   * If the validity changes to valid, it will set the model to the last available valid
   * `$modelValue`, i.e. either the last parsed value or the last value set from the scope.
   */
  $validate: function() {
    // ignore $validate before model is initialized
    if (isNumberNaN(this.$modelValue)) {
      return;
    }

    var viewValue = this.$$lastCommittedViewValue;
    // Note: we use the $$rawModelValue as $modelValue might have been
    // set to undefined during a view -> model update that found validation
    // errors. We can't parse the view here, since that could change
    // the model although neither viewValue nor the model on the scope changed
    var modelValue = this.$$rawModelValue;

    var prevValid = this.$valid;
    var prevModelValue = this.$modelValue;

    var allowInvalid = this.$options.getOption('allowInvalid');

    var that = this;
    this.$$runValidators(modelValue, viewValue, function(allValid) {
      // If there was no change in validity, don't update the model
      // This prevents changing an invalid modelValue to undefined
      if (!allowInvalid && prevValid !== allValid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;

        if (that.$modelValue !== prevModelValue) {
          that.$$writeModelToScope();
        }
      }
    });
  },

  $$runValidators: function(modelValue, viewValue, doneCallback) {
    this.$$currentValidationRunId++;
    var localValidationRunId = this.$$currentValidationRunId;
    var that = this;

    // check parser error
    if (!processParseErrors()) {
      validationDone(false);
      return;
    }
    if (!processSyncValidators()) {
      validationDone(false);
      return;
    }
    processAsyncValidators();

    function processParseErrors() {
      var errorKey = that.$$parserName || 'parse';
      if (isUndefined(that.$$parserValid)) {
        setValidity(errorKey, null);
      } else {
        if (!that.$$parserValid) {
          forEach(that.$validators, function(v, name) {
            setValidity(name, null);
          });
          forEach(that.$asyncValidators, function(v, name) {
            setValidity(name, null);
          });
        }
        // Set the parse error last, to prevent unsetting it, should a $validators key == parserName
        setValidity(errorKey, that.$$parserValid);
        return that.$$parserValid;
      }
      return true;
    }

    function processSyncValidators() {
      var syncValidatorsValid = true;
      forEach(that.$validators, function(validator, name) {
        var result = Boolean(validator(modelValue, viewValue));
        syncValidatorsValid = syncValidatorsValid && result;
        setValidity(name, result);
      });
      if (!syncValidatorsValid) {
        forEach(that.$asyncValidators, function(v, name) {
          setValidity(name, null);
        });
        return false;
      }
      return true;
    }

    function processAsyncValidators() {
      var validatorPromises = [];
      var allValid = true;
      forEach(that.$asyncValidators, function(validator, name) {
        var promise = validator(modelValue, viewValue);
        if (!isPromiseLike(promise)) {
          throw ngModelMinErr('nopromise',
            'Expected asynchronous validator to return a promise but got \'{0}\' instead.', promise);
        }
        setValidity(name, undefined);
        validatorPromises.push(promise.then(function() {
          setValidity(name, true);
        }, function() {
          allValid = false;
          setValidity(name, false);
        }));
      });
      if (!validatorPromises.length) {
        validationDone(true);
      } else {
        that.$$q.all(validatorPromises).then(function() {
          validationDone(allValid);
        }, noop);
      }
    }

    function setValidity(name, isValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {
        that.$setValidity(name, isValid);
      }
    }

    function validationDone(allValid) {
      if (localValidationRunId === that.$$currentValidationRunId) {

        doneCallback(allValid);
      }
    }
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$commitViewValue
   *
   * @description
   * Commit a pending update to the `$modelValue`.
   *
   * Updates may be pending by a debounced event or because the input is waiting for a some future
   * event defined in `ng-model-options`. this method is rarely needed as `NgModelController`
   * usually handles calling this in response to input events.
   */
  $commitViewValue: function() {
    var viewValue = this.$viewValue;

    this.$$timeout.cancel(this.$$pendingDebounce);

    // If the view value has not changed then we should just exit, except in the case where there is
    // a native validator on the element. In this case the validation state may have changed even though
    // the viewValue has stayed empty.
    if (this.$$lastCommittedViewValue === viewValue && (viewValue !== '' || !this.$$hasNativeValidators)) {
      return;
    }
    this.$$updateEmptyClasses(viewValue);
    this.$$lastCommittedViewValue = viewValue;

    // change to dirty
    if (this.$pristine) {
      this.$setDirty();
    }
    this.$$parseAndValidate();
  },

  $$parseAndValidate: function() {
    var viewValue = this.$$lastCommittedViewValue;
    var modelValue = viewValue;
    var that = this;

    this.$$parserValid = isUndefined(modelValue) ? undefined : true;

    if (this.$$parserValid) {
      for (var i = 0; i < this.$parsers.length; i++) {
        modelValue = this.$parsers[i](modelValue);
        if (isUndefined(modelValue)) {
          this.$$parserValid = false;
          break;
        }
      }
    }
    if (isNumberNaN(this.$modelValue)) {
      // this.$modelValue has not been touched yet...
      this.$modelValue = this.$$ngModelGet(this.$$scope);
    }
    var prevModelValue = this.$modelValue;
    var allowInvalid = this.$options.getOption('allowInvalid');
    this.$$rawModelValue = modelValue;

    if (allowInvalid) {
      this.$modelValue = modelValue;
      writeToModelIfNeeded();
    }

    // Pass the $$lastCommittedViewValue here, because the cached viewValue might be out of date.
    // This can happen if e.g. $setViewValue is called from inside a parser
    this.$$runValidators(modelValue, this.$$lastCommittedViewValue, function(allValid) {
      if (!allowInvalid) {
        // Note: Don't check this.$valid here, as we could have
        // external validators (e.g. calculated on the server),
        // that just call $setValidity and need the model value
        // to calculate their validity.
        that.$modelValue = allValid ? modelValue : undefined;
        writeToModelIfNeeded();
      }
    });

    function writeToModelIfNeeded() {
      if (that.$modelValue !== prevModelValue) {
        that.$$writeModelToScope();
      }
    }
  },

  $$writeModelToScope: function() {
    this.$$ngModelSet(this.$$scope, this.$modelValue);
    forEach(this.$viewChangeListeners, function(listener) {
      try {
        listener();
      } catch (e) {
        // eslint-disable-next-line no-invalid-this
        this.$$exceptionHandler(e);
      }
    }, this);
  },

  /**
   * @ngdoc method
   * @name ngModel.NgModelController#$setViewValue
   *
   * @description
   * Update the view value.
   *
   * This method should be called when a control wants to change the view value; typically,
   * this is done from within a DOM event handler. For example, the {@link ng.directive:input input}
   * directive calls it when the value of the input changes and {@link ng.directive:select select}
   * calls it when an option is selected.
   *
   * When `$setViewValue` is called, the new `value` will be staged for committing through the `$parsers`
   * and `$validators` pipelines. If there are no special {@link ngModelOptions} specified then the staged
   * value is sent directly for processing through the `$parsers` pipeline. After this, the `$validators` and
   * `$asyncValidators` are called and the value is applied to `$modelValue`.
   * Finally, the value is set to the **expression** specified in the `ng-model` attribute and
   * all the registered change listeners, in the `$viewChangeListeners` list are called.
   *
   * In case the {@link ng.directive:ngModelOptions ngModelOptions} directive is used with `updateOn`
   * and the `default` trigger is not listed, all those actions will remain pending until one of the
   * `updateOn` events is triggered on the DOM element.
   * All these actions will be debounced if the {@link ng.directive:ngModelOptions ngModelOptions}
   * directive is used with a custom debounce for this particular event.
   * Note that a `$digest` is only triggered once the `updateOn` events are fired, or if `debounce`
   * is specified, once the timer runs out.
   *
   * When used with standard inputs, the view value will always be a string (which is in some cases
   * parsed into another type, such as a `Date` object for `input[date]`.)
   * However, custom controls might also pass objects to this method. In this case, we should make
   * a copy of the object before passing it to `$setViewValue`. This is because `ngModel` does not
   * perform a deep watch of objects, it only looks for a change of identity. If you only change
   * the property of the object then ngModel will not realize that the object has changed and
   * will not invoke the `$parsers` and `$validators` pipelines. For this reason, you should
   * not change properties of the copy once it has been passed to `$setViewValue`.
   * Otherwise you may cause the model value on the scope to change incorrectly.
   *
   * <div class="alert alert-info">
   * In any case, the value passed to the method should always reflect the current value
   * of the control. For example, if you are calling `$setViewValue` for an input element,
   * you should pass the input DOM value. Otherwise, the control and the scope model become
   * out of sync. It's also important to note that `$setViewValue` does not call `$render` or change
   * the control's DOM value in any way. If we want to change the control's DOM value
   * programmatically, we should update the `ngModel` scope expression. Its new value will be
   * picked up by the model controller, which will run it through the `$formatters`, `$render` it
   * to update the DOM, and finally call `$validate` on it.
   * </div>
   *
   * @param {*} value value from the view.
   * @param {string} trigger Event that triggered the update.
   */
  $setViewValue: function(value, trigger) {
    this.$viewValue = value;
    if (this.$options.getOption('updateOnDefault')) {
      this.$$debounceViewValueCommit(trigger);
    }
  },

  $$debounceViewValueCommit: function(trigger) {
    var debounceDelay = this.$options.getOption('debounce');

    if (isNumber(debounceDelay[trigger])) {
      debounceDelay = debounceDelay[trigger];
    } else if (isNumber(debounceDelay['default'])) {
      debounceDelay = debounceDelay['default'];
    }

    this.$$timeout.cancel(this.$$pendingDebounce);
    var that = this;
    if (debounceDelay > 0) { // this fails if debounceDelay is an object
      this.$$pendingDebounce = this.$$timeout(function() {
        that.$commitViewValue();
      }, debounceDelay);
    } else if (this.$$scope.$root.$$phase) {
      this.$commitViewValue();
    } else {
      this.$$scope.$apply(function() {
        that.$commitViewValue();
      });
    }
  },

  /**
   * @ngdoc method
   *
   * @name ngModel.NgModelController#$overrideModelOptions
   *
   * @description
   *
   * Override the current model options settings programmatically.
   *
   * The previous `ModelOptions` value will not be modified. Instead, a
   * new `ModelOptions` object will inherit from the previous one overriding
   * or inheriting settings that are defined in the given parameter.
   *
   * See {@link ngModelOptions} for information about what options can be specified
   * and how model option inheritance works.
   *
   * @param {Object} options a hash of settings to override the previous options
   *
   */
  $overrideModelOptions: function(options) {
    this.$options = this.$options.createChild(options);
  }
};

function setupModelWatcher(ctrl) {
  // model -> value
  // Note: we cannot use a normal scope.$watch as we want to detect the following:
  // 1. scope value is 'a'
  // 2. user enters 'b'
  // 3. ng-change kicks in and reverts scope value to 'a'
  //    -> scope value did not change since the last digest as
  //       ng-change executes in apply phase
  // 4. view should be changed back to 'a'
  ctrl.$$scope.$watch(function ngModelWatch(scope) {
    var modelValue = ctrl.$$ngModelGet(scope);

    // if scope model value and ngModel value are out of sync
    // TODO(perf): why not move this to the action fn?
    if (modelValue !== ctrl.$modelValue &&
       // checks for NaN is needed to allow setting the model to NaN when there's an asyncValidator
        // eslint-disable-next-line no-self-compare
       (ctrl.$modelValue === ctrl.$modelValue || modelValue === modelValue)
    ) {
      ctrl.$modelValue = ctrl.$$rawModelValue = modelValue;
      ctrl.$$parserValid = undefined;

      var formatters = ctrl.$formatters,
          idx = formatters.length;

      var viewValue = modelValue;
      while (idx--) {
        viewValue = formatters[idx](viewValue);
      }
      if (ctrl.$viewValue !== viewValue) {
        ctrl.$$updateEmptyClasses(viewValue);
        ctrl.$viewValue = ctrl.$$lastCommittedViewValue = viewValue;
        ctrl.$render();

        // It is possible that model and view value have been updated during render
        ctrl.$$runValidators(ctrl.$modelValue, ctrl.$viewValue, noop);
      }
    }

    return modelValue;
  });
}

/**
 * @ngdoc method
 * @name ngModel.NgModelController#$setValidity
 *
 * @description
 * Change the validity state, and notify the form.
 *
 * This method can be called within $parsers/$formatters or a custom validation implementation.
 * However, in most cases it should be sufficient to use the `ngModel.$validators` and
 * `ngModel.$asyncValidators` collections which will call `$setValidity` automatically.
 *
 * @param {string} validationErrorKey Name of the validator. The `validationErrorKey` will be assigned
 *        to either `$error[validationErrorKey]` or `$pending[validationErrorKey]`
 *        (for unfulfilled `$asyncValidators`), so that it is available for data-binding.
 *        The `validationErrorKey` should be in camelCase and will get converted into dash-case
 *        for class name. Example: `myError` will result in `ng-valid-my-error` and `ng-invalid-my-error`
 *        classes and can be bound to as `{{ someForm.someControl.$error.myError }}`.
 * @param {boolean} isValid Whether the current state is valid (true), invalid (false), pending (undefined),
 *                          or skipped (null). Pending is used for unfulfilled `$asyncValidators`.
 *                          Skipped is used by Angular when validators do not run because of parse errors and
 *                          when `$asyncValidators` do not run because any of the `$validators` failed.
 */
addSetValidityMethod({
  clazz: NgModelController,
  set: function(object, property) {
    object[property] = true;
  },
  unset: function(object, property) {
    delete object[property];
  }
});


/**
 * @ngdoc directive
 * @name ngModel
 *
 * @element input
 * @priority 1
 *
 * @description
 * The `ngModel` directive binds an `input`,`select`, `textarea` (or custom form control) to a
 * property on the scope using {@link ngModel.NgModelController NgModelController},
 * which is created and exposed by this directive.
 *
 * `ngModel` is responsible for:
 *
 * - Binding the view into the model, which other directives such as `input`, `textarea` or `select`
 *   require.
 * - Providing validation behavior (i.e. required, number, email, url).
 * - Keeping the state of the control (valid/invalid, dirty/pristine, touched/untouched, validation errors).
 * - Setting related css classes on the element (`ng-valid`, `ng-invalid`, `ng-dirty`, `ng-pristine`, `ng-touched`,
 *   `ng-untouched`, `ng-empty`, `ng-not-empty`) including animations.
 * - Registering the control with its parent {@link ng.directive:form form}.
 *
 * Note: `ngModel` will try to bind to the property given by evaluating the expression on the
 * current scope. If the property doesn't already exist on this scope, it will be created
 * implicitly and added to the scope.
 *
 * For best practices on using `ngModel`, see:
 *
 *  - [Understanding Scopes](https://github.com/angular/angular.js/wiki/Understanding-Scopes)
 *
 * For basic examples, how to use `ngModel`, see:
 *
 *  - {@link ng.directive:input input}
 *    - {@link input[text] text}
 *    - {@link input[checkbox] checkbox}
 *    - {@link input[radio] radio}
 *    - {@link input[number] number}
 *    - {@link input[email] email}
 *    - {@link input[url] url}
 *    - {@link input[date] date}
 *    - {@link input[datetime-local] datetime-local}
 *    - {@link input[time] time}
 *    - {@link input[month] month}
 *    - {@link input[week] week}
 *  - {@link ng.directive:select select}
 *  - {@link ng.directive:textarea textarea}
 *
 * # Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding inputs to models that are objects (e.g. `Date`) or collections (e.g. arrays). If only properties of the
 * object or collection change, `ngModel` will not be notified and so the input will not be  re-rendered.
 *
 * The model must be assigned an entirely new object or collection before a re-rendering will occur.
 *
 * Some directives have options that will cause them to use a custom `$watchCollection` on the model expression
 * - for example, `ngOptions` will do so when a `track by` clause is included in the comprehension expression or
 * if the select is given the `multiple` attribute.
 *
 * The `$watchCollection()` method only does a shallow comparison, meaning that changing properties deeper than the
 * first level of the object (or only changing the properties of an item in the collection if it's an array) will still
 * not trigger a re-rendering of the model.
 *
 * # CSS classes
 * The following CSS classes are added and removed on the associated input/select/textarea element
 * depending on the validity of the model.
 *
 *  - `ng-valid`: the model is valid
 *  - `ng-invalid`: the model is invalid
 *  - `ng-valid-[key]`: for each valid key added by `$setValidity`
 *  - `ng-invalid-[key]`: for each invalid key added by `$setValidity`
 *  - `ng-pristine`: the control hasn't been interacted with yet
 *  - `ng-dirty`: the control has been interacted with
 *  - `ng-touched`: the control has been blurred
 *  - `ng-untouched`: the control hasn't been blurred
 *  - `ng-pending`: any `$asyncValidators` are unfulfilled
 *  - `ng-empty`: the view does not contain a value or the value is deemed "empty", as defined
 *     by the {@link ngModel.NgModelController#$isEmpty} method
 *  - `ng-not-empty`: the view contains a non-empty value
 *
 * Keep in mind that ngAnimate can detect each of these classes when added and removed.
 *
 * ## Animation Hooks
 *
 * Animations within models are triggered when any of the associated CSS classes are added and removed
 * on the input element which is attached to the model. These classes include: `.ng-pristine`, `.ng-dirty`,
 * `.ng-invalid` and `.ng-valid` as well as any other validations that are performed on the model itself.
 * The animations that are triggered within ngModel are similar to how they work in ngClass and
 * animations can be hooked into using CSS transitions, keyframes as well as JS animations.
 *
 * The following example shows a simple way to utilize CSS transitions to style an input element
 * that has been rendered as invalid after it has been validated:
 *
 * <pre>
 * //be sure to include ngAnimate as a module to hook into more
 * //advanced animations
 * .my-input {
 *   transition:0.5s linear all;
 *   background: white;
 * }
 * .my-input.ng-invalid {
 *   background: red;
 *   color:white;
 * }
 * </pre>
 *
 * @example
 * <example deps="angular-animate.js" animations="true" fixBase="true" module="inputExample" name="ng-model">
     <file name="index.html">
       <script>
        angular.module('inputExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.val = '1';
          }]);
       </script>
       <style>
         .my-input {
           transition:all linear 0.5s;
           background: transparent;
         }
         .my-input.ng-invalid {
           color:white;
           background: red;
         }
       </style>
       <p id="inputDescription">
        Update input to see transitions when valid/invalid.
        Integer is a valid value.
       </p>
       <form name="testForm" ng-controller="ExampleController">
         <input ng-model="val" ng-pattern="/^\d+$/" name="anim" class="my-input"
                aria-describedby="inputDescription" />
       </form>
     </file>
 * </example>
 *
 * ## Binding to a getter/setter
 *
 * Sometimes it's helpful to bind `ngModel` to a getter/setter function.  A getter/setter is a
 * function that returns a representation of the model when called with zero arguments, and sets
 * the internal state of a model when called with an argument. It's sometimes useful to use this
 * for models that have an internal representation that's different from what the model exposes
 * to the view.
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's best to keep getters fast because Angular is likely to call them more
 * frequently than other parts of your code.
 * </div>
 *
 * You use this behavior by adding `ng-model-options="{ getterSetter: true }"` to an element that
 * has `ng-model` attached to it. You can also add `ng-model-options="{ getterSetter: true }"` to
 * a `<form>`, which will enable this behavior for all `<input>`s within it. See
 * {@link ng.directive:ngModelOptions `ngModelOptions`} for more.
 *
 * The following example shows how to use `ngModel` with a getter/setter:
 *
 * @example
 * <example name="ngModel-getter-setter" module="getterSetterExample">
     <file name="index.html">
       <div ng-controller="ExampleController">
         <form name="userForm">
           <label>Name:
             <input type="text" name="userName"
                    ng-model="user.name"
                    ng-model-options="{ getterSetter: true }" />
           </label>
         </form>
         <pre>user.name = <span ng-bind="user.name()"></span></pre>
       </div>
     </file>
     <file name="app.js">
       angular.module('getterSetterExample', [])
         .controller('ExampleController', ['$scope', function($scope) {
           var _name = 'Brian';
           $scope.user = {
             name: function(newName) {
              // Note that newName can be undefined for two reasons:
              // 1. Because it is called as a getter and thus called with no arguments
              // 2. Because the property should actually be set to undefined. This happens e.g. if the
              //    input is invalid
              return arguments.length ? (_name = newName) : _name;
             }
           };
         }]);
     </file>
 * </example>
 */
var ngModelDirective = ['$rootScope', function($rootScope) {
  return {
    restrict: 'A',
    require: ['ngModel', '^?form', '^?ngModelOptions'],
    controller: NgModelController,
    // Prelink needs to run before any input directive
    // so that we can set the NgModelOptions in NgModelController
    // before anyone else uses it.
    priority: 1,
    compile: function ngModelCompile(element) {
      // Setup initial state of the control
      element.addClass(PRISTINE_CLASS).addClass(UNTOUCHED_CLASS).addClass(VALID_CLASS);

      return {
        pre: function ngModelPreLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0],
              formCtrl = ctrls[1] || modelCtrl.$$parentForm,
              optionsCtrl = ctrls[2];

          if (optionsCtrl) {
            modelCtrl.$options = optionsCtrl.$options;
          }

          modelCtrl.$$initGetterSetters();

          // notify others, especially parent forms
          formCtrl.$addControl(modelCtrl);

          attr.$observe('name', function(newValue) {
            if (modelCtrl.$name !== newValue) {
              modelCtrl.$$parentForm.$$renameControl(modelCtrl, newValue);
            }
          });

          scope.$on('$destroy', function() {
            modelCtrl.$$parentForm.$removeControl(modelCtrl);
          });
        },
        post: function ngModelPostLink(scope, element, attr, ctrls) {
          var modelCtrl = ctrls[0];
          if (modelCtrl.$options.getOption('updateOn')) {
            element.on(modelCtrl.$options.getOption('updateOn'), function(ev) {
              modelCtrl.$$debounceViewValueCommit(ev && ev.type);
            });
          }

          function setTouched() {
            modelCtrl.$setTouched();
          }

          element.on('blur', function() {
            if (modelCtrl.$touched) return;

            if ($rootScope.$$phase) {
              scope.$evalAsync(setTouched);
            } else {
              scope.$apply(setTouched);
            }
          });
        }
      };
    }
  };
}];

/* exported defaultModelOptions */
var defaultModelOptions;
var DEFAULT_REGEXP = /(\s+|^)default(\s+|$)/;

/**
 * @ngdoc type
 * @name ModelOptions
 * @description
 * A container for the options set by the {@link ngModelOptions} directive
 */
function ModelOptions(options) {
  this.$$options = options;
}

ModelOptions.prototype = {

  /**
   * @ngdoc method
   * @name ModelOptions#getOption
   * @param {string} name the name of the option to retrieve
   * @returns {*} the value of the option
   * @description
   * Returns the value of the given option
   */
  getOption: function(name) {
    return this.$$options[name];
  },

  /**
   * @ngdoc method
   * @name ModelOptions#createChild
   * @param {Object} options a hash of options for the new child that will override the parent's options
   * @return {ModelOptions} a new `ModelOptions` object initialized with the given options.
   */
  createChild: function(options) {
    var inheritAll = false;

    // make a shallow copy
    options = extend({}, options);

    // Inherit options from the parent if specified by the value `"$inherit"`
    forEach(options, /* @this */ function(option, key) {
      if (option === '$inherit') {
        if (key === '*') {
          inheritAll = true;
        } else {
          options[key] = this.$$options[key];
          // `updateOn` is special so we must also inherit the `updateOnDefault` option
          if (key === 'updateOn') {
            options.updateOnDefault = this.$$options.updateOnDefault;
          }
        }
      } else {
        if (key === 'updateOn') {
          // If the `updateOn` property contains the `default` event then we have to remove
          // it from the event list and set the `updateOnDefault` flag.
          options.updateOnDefault = false;
          options[key] = trim(option.replace(DEFAULT_REGEXP, function() {
            options.updateOnDefault = true;
            return ' ';
          }));
        }
      }
    }, this);

    if (inheritAll) {
      // We have a property of the form: `"*": "$inherit"`
      delete options['*'];
      defaults(options, this.$$options);
    }

    // Finally add in any missing defaults
    defaults(options, defaultModelOptions.$$options);

    return new ModelOptions(options);
  }
};


defaultModelOptions = new ModelOptions({
  updateOn: '',
  updateOnDefault: true,
  debounce: 0,
  getterSetter: false,
  allowInvalid: false,
  timezone: null
});


/**
 * @ngdoc directive
 * @name ngModelOptions
 *
 * @description
 * This directive allows you to modify the behaviour of {@link ngModel} directives within your
 * application. You can specify an `ngModelOptions` directive on any element. All {@link ngModel}
 * directives will use the options of their nearest `ngModelOptions` ancestor.
 *
 * The `ngModelOptions` settings are found by evaluating the value of the attribute directive as
 * an Angular expression. This expression should evaluate to an object, whose properties contain
 * the settings. For example: `<div "ng-model-options"="{ debounce: 100 }"`.
 *
 * ## Inheriting Options
 *
 * You can specify that an `ngModelOptions` setting should be inherited from a parent `ngModelOptions`
 * directive by giving it the value of `"$inherit"`.
 * Then it will inherit that setting from the first `ngModelOptions` directive found by traversing up the
 * DOM tree. If there is no ancestor element containing an `ngModelOptions` directive then default settings
 * will be used.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', allowInvalid: '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', allowInvalid: '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 0 }
 * ```
 *
 * Notice that the `debounce` setting was not inherited and used the default value instead.
 *
 * You can specify that all undefined settings are automatically inherited from an ancestor by
 * including a property with key of `"*"` and value of `"$inherit"`.
 *
 * For example given the following fragment of HTML
 *
 *
 * ```html
 * <div ng-model-options="{ allowInvalid: true, debounce: 200 }">
 *   <form ng-model-options="{ updateOn: 'blur', "*": '$inherit' }">
 *     <input ng-model-options="{ updateOn: 'default', "*": '$inherit' }" />
 *   </form>
 * </div>
 * ```
 *
 * the `input` element will have the following settings
 *
 * ```js
 * { allowInvalid: true, updateOn: 'default', debounce: 200 }
 * ```
 *
 * Notice that the `debounce` setting now inherits the value from the outer `<div>` element.
 *
 * If you are creating a reusable component then you should be careful when using `"*": "$inherit"`
 * since you may inadvertently inherit a setting in the future that changes the behavior of your component.
 *
 *
 * ## Triggering and debouncing model updates
 *
 * The `updateOn` and `debounce` properties allow you to specify a custom list of events that will
 * trigger a model update and/or a debouncing delay so that the actual update only takes place when
 * a timer expires; this timer will be reset after another change takes place.
 *
 * Given the nature of `ngModelOptions`, the value displayed inside input fields in the view might
 * be different from the value in the actual model. This means that if you update the model you
 * should also invoke {@link ngModel.NgModelController#$rollbackViewValue} on the relevant input field in
 * order to make sure it is synchronized with the model and that any debounced action is canceled.
 *
 * The easiest way to reference the control's {@link ngModel.NgModelController#$rollbackViewValue}
 * method is by making sure the input is placed inside a form that has a `name` attribute. This is
 * important because `form` controllers are published to the related scope under the name in their
 * `name` attribute.
 *
 * Any pending changes will take place immediately when an enclosing form is submitted via the
 * `submit` event. Note that `ngClick` events will occur before the model is updated. Use `ngSubmit`
 * to have access to the updated model.
 *
 * The following example shows how to override immediate updates. Changes on the inputs within the
 * form will update the model only when the control loses focus (blur event). If `escape` key is
 * pressed while the input field is focused, the value is reset to the value in the current model.
 *
 * <example name="ngModelOptions-directive-blur" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ updateOn: 'blur' }"
 *                  ng-keyup="cancel($event)" />
 *         </label><br />
 *         <label>
 *           Other data:
 *           <input type="text" ng-model="user.data" />
 *         </label><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say', data: '' };
 *
 *         $scope.cancel = function(e) {
 *           if (e.keyCode === 27) {
 *             $scope.userForm.userName.$rollbackViewValue();
 *           }
 *         };
 *       }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     var model = element(by.binding('user.name'));
 *     var input = element(by.model('user.name'));
 *     var other = element(by.model('user.data'));
 *
 *     it('should allow custom events', function() {
 *       input.sendKeys(' hello');
 *       input.click();
 *       expect(model.getText()).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say hello');
 *     });
 *
 *     it('should $rollbackViewValue when model changes', function() {
 *       input.sendKeys(' hello');
 *       expect(input.getAttribute('value')).toEqual('say hello');
 *       input.sendKeys(protractor.Key.ESCAPE);
 *       expect(input.getAttribute('value')).toEqual('say');
 *       other.click();
 *       expect(model.getText()).toEqual('say');
 *     });
 *   </file>
 * </example>
 *
 * The next example shows how to debounce model changes. Model will be updated only 1 sec after last change.
 * If the `Clear` button is pressed, any debounced action is canceled and the value becomes empty.
 *
 * <example name="ngModelOptions-directive-debounce" module="optionsExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         Name:
 *         <input type="text" name="userName"
 *                ng-model="user.name"
 *                ng-model-options="{ debounce: 1000 }" />
 *         <button ng-click="userForm.userName.$rollbackViewValue(); user.name=''">Clear</button><br />
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('optionsExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.user = { name: 'say' };
 *       }]);
 *   </file>
 * </example>
 *
 * ## Model updates and validation
 *
 * The default behaviour in `ngModel` is that the model value is set to `undefined` when the
 * validation determines that the value is invalid. By setting the `allowInvalid` property to true,
 * the model will still be updated even if the value is invalid.
 *
 *
 * ## Connecting to the scope
 *
 * By setting the `getterSetter` property to true you are telling ngModel that the `ngModel` expression
 * on the scope refers to a "getter/setter" function rather than the value itself.
 *
 * The following example shows how to bind to getter/setters:
 *
 * <example name="ngModelOptions-directive-getter-setter" module="getterSetterExample">
 *   <file name="index.html">
 *     <div ng-controller="ExampleController">
 *       <form name="userForm">
 *         <label>
 *           Name:
 *           <input type="text" name="userName"
 *                  ng-model="user.name"
 *                  ng-model-options="{ getterSetter: true }" />
 *         </label>
 *       </form>
 *       <pre>user.name = <span ng-bind="user.name()"></span></pre>
 *     </div>
 *   </file>
 *   <file name="app.js">
 *     angular.module('getterSetterExample', [])
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         var _name = 'Brian';
 *         $scope.user = {
 *           name: function(newName) {
 *             return angular.isDefined(newName) ? (_name = newName) : _name;
 *           }
 *         };
 *       }]);
 *   </file>
 * </example>
 *
 *
 * ## Specifying timezones
 *
 * You can specify the timezone that date/time input directives expect by providing its name in the
 * `timezone` property.
 *
 * @param {Object} ngModelOptions options to apply to {@link ngModel} directives on this element and
 *   and its descendents. Valid keys are:
 *   - `updateOn`: string specifying which event should the input be bound to. You can set several
 *     events using an space delimited list. There is a special event called `default` that
 *     matches the default events belonging to the control.
 *   - `debounce`: integer value which contains the debounce model update value in milliseconds. A
 *     value of 0 triggers an immediate update. If an object is supplied instead, you can specify a
 *     custom value for each event. For example:
 *     ```
 *     ng-model-options="{
 *       updateOn: 'default blur',
 *       debounce: { 'default': 500, 'blur': 0 }
 *     }"
 *     ```
 *   - `allowInvalid`: boolean value which indicates that the model can be set with values that did
 *     not validate correctly instead of the default behavior of setting the model to undefined.
 *   - `getterSetter`: boolean value which determines whether or not to treat functions bound to
 *     `ngModel` as getters/setters.
 *   - `timezone`: Defines the timezone to be used to read/write the `Date` instance in the model for
 *     `<input type="date" />`, `<input type="time" />`, ... . It understands UTC/GMT and the
 *     continental US time zone abbreviations, but for general use, use a time zone offset, for
 *     example, `'+0430'` (4 hours, 30 minutes east of the Greenwich meridian)
 *     If not specified, the timezone of the browser will be used.
 *
 */
var ngModelOptionsDirective = function() {
  NgModelOptionsController.$inject = ['$attrs', '$scope'];
  function NgModelOptionsController($attrs, $scope) {
    this.$$attrs = $attrs;
    this.$$scope = $scope;
  }
  NgModelOptionsController.prototype = {
    $onInit: function() {
      var parentOptions = this.parentCtrl ? this.parentCtrl.$options : defaultModelOptions;
      var modelOptionsDefinition = this.$$scope.$eval(this.$$attrs.ngModelOptions);

      this.$options = parentOptions.createChild(modelOptionsDefinition);
    }
  };

  return {
    restrict: 'A',
    // ngModelOptions needs to run before ngModel and input directives
    priority: 10,
    require: {parentCtrl: '?^^ngModelOptions'},
    bindToController: true,
    controller: NgModelOptionsController
  };
};


// shallow copy over values from `src` that are not already specified on `dst`
function defaults(dst, src) {
  forEach(src, function(value, key) {
    if (!isDefined(dst[key])) {
      dst[key] = value;
    }
  });
}

/**
 * @ngdoc directive
 * @name ngNonBindable
 * @restrict AC
 * @priority 1000
 *
 * @description
 * The `ngNonBindable` directive tells Angular not to compile or bind the contents of the current
 * DOM element. This is useful if the element contains what appears to be Angular directives and
 * bindings but which should be ignored by Angular. This could be the case if you have a site that
 * displays snippets of code, for instance.
 *
 * @element ANY
 *
 * @example
 * In this example there are two locations where a simple interpolation binding (`{{}}`) is present,
 * but the one wrapped in `ngNonBindable` is left alone.
 *
 * @example
    <example name="ng-non-bindable">
      <file name="index.html">
        <div>Normal: {{1 + 2}}</div>
        <div ng-non-bindable>Ignored: {{1 + 2}}</div>
      </file>
      <file name="protractor.js" type="protractor">
       it('should check ng-non-bindable', function() {
         expect(element(by.binding('1 + 2')).getText()).toContain('3');
         expect(element.all(by.css('div')).last().getText()).toMatch(/1 \+ 2/);
       });
      </file>
    </example>
 */
var ngNonBindableDirective = ngDirective({ terminal: true, priority: 1000 });

/* exported ngOptionsDirective */

/* global jqLiteRemove */

var ngOptionsMinErr = minErr('ngOptions');

/**
 * @ngdoc directive
 * @name ngOptions
 * @restrict A
 *
 * @description
 *
 * The `ngOptions` attribute can be used to dynamically generate a list of `<option>`
 * elements for the `<select>` element using the array or object obtained by evaluating the
 * `ngOptions` comprehension expression.
 *
 * In many cases, {@link ng.directive:ngRepeat ngRepeat} can be used on `<option>` elements instead of
 * `ngOptions` to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * When an item in the `<select>` menu is selected, the array element or object property
 * represented by the selected option will be bound to the model identified by the `ngModel`
 * directive.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Complex Models (objects or collections)
 *
 * By default, `ngModel` watches the model by reference, not value. This is important to know when
 * binding the select to a model that is an object or a collection.
 *
 * One issue occurs if you want to preselect an option. For example, if you set
 * the model to an object that is equal to an object in your collection, `ngOptions` won't be able to set the selection,
 * because the objects are not identical. So by default, you should always reference the item in your collection
 * for preselections, e.g.: `$scope.selected = $scope.collection[3]`.
 *
 * Another solution is to use a `track by` clause, because then `ngOptions` will track the identity
 * of the item not by reference, but by the result of the `track by` expression. For example, if your
 * collection items have an id property, you would `track by item.id`.
 *
 * A different issue with objects or collections is that ngModel won't detect if an object property or
 * a collection item changes. For that reason, `ngOptions` additionally watches the model using
 * `$watchCollection`, when the expression contains a `track by` clause or the the select has the `multiple` attribute.
 * This allows ngOptions to trigger a re-rendering of the options even if the actual object/collection
 * has not changed identity, but only a property on the object or an item in the collection changes.
 *
 * Note that `$watchCollection` does a shallow comparison of the properties of the object (or the items in the collection
 * if the model is an array). This means that changing a property deeper than the first level inside the
 * object/collection will not trigger a re-rendering.
 *
 * ## `select` **`as`**
 *
 * Using `select` **`as`** will bind the result of the `select` expression to the model, but
 * the value of the `<select>` and `<option>` html elements will be either the index (for array data sources)
 * or property name (for object data sources) of the value within the collection. If a **`track by`** expression
 * is used, the result of that expression will be set as the value of the `option` and `select` elements.
 *
 *
 * ### `select` **`as`** and **`track by`**
 *
 * <div class="alert alert-warning">
 * Be careful when using `select` **`as`** and **`track by`** in the same expression.
 * </div>
 *
 * Given this array of items on the $scope:
 *
 * ```js
 * $scope.items = [{
 *   id: 1,
 *   label: 'aLabel',
 *   subItem: { name: 'aSubItem' }
 * }, {
 *   id: 2,
 *   label: 'bLabel',
 *   subItem: { name: 'bSubItem' }
 * }];
 * ```
 *
 * This will work:
 *
 * ```html
 * <select ng-options="item as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0];
 * ```
 *
 * but this will not work:
 *
 * ```html
 * <select ng-options="item.subItem as item.label for item in items track by item.id" ng-model="selected"></select>
 * ```
 * ```js
 * $scope.selected = $scope.items[0].subItem;
 * ```
 *
 * In both examples, the **`track by`** expression is applied successfully to each `item` in the
 * `items` array. Because the selected option has been set programmatically in the controller, the
 * **`track by`** expression is also applied to the `ngModel` value. In the first example, the
 * `ngModel` value is `items[0]` and the **`track by`** expression evaluates to `items[0].id` with
 * no issue. In the second example, the `ngModel` value is `items[0].subItem` and the **`track by`**
 * expression evaluates to `items[0].subItem.id` (which is undefined). As a result, the model value
 * is not matched against any `<option>` and the `<select>` appears as having no selected value.
 *
 *
 * @param {string} ngModel Assignable AngularJS expression to data-bind to.
 * @param {comprehension_expression} ngOptions in one of the following forms:
 *
 *   * for array data sources:
 *     * `label` **`for`** `value` **`in`** `array`
 *     * `select` **`as`** `label` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array`
 *     * `label` **`group by`** `group` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`disable when`** `disable` **`for`** `value` **`in`** `array` **`track by`** `trackexpr`
 *     * `label` **`for`** `value` **`in`** `array` | orderBy:`orderexpr` **`track by`** `trackexpr`
 *        (for including a filter with `track by`)
 *   * for object data sources:
 *     * `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`for (`**`key` **`,`** `value`**`) in`** `object`
 *     * `label` **`group by`** `group` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `label` **`disable when`** `disable` **`for (`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`group by`** `group`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *     * `select` **`as`** `label` **`disable when`** `disable`
 *         **`for` `(`**`key`**`,`** `value`**`) in`** `object`
 *
 * Where:
 *
 *   * `array` / `object`: an expression which evaluates to an array / object to iterate over.
 *   * `value`: local variable which will refer to each item in the `array` or each property value
 *      of `object` during iteration.
 *   * `key`: local variable which will refer to a property name in `object` during iteration.
 *   * `label`: The result of this expression will be the label for `<option>` element. The
 *     `expression` will most likely refer to the `value` variable (e.g. `value.propertyName`).
 *   * `select`: The result of this expression will be bound to the model of the parent `<select>`
 *      element. If not specified, `select` expression will default to `value`.
 *   * `group`: The result of this expression will be used to group options using the `<optgroup>`
 *      DOM element.
 *   * `disable`: The result of this expression will be used to disable the rendered `<option>`
 *      element. Return `true` to disable.
 *   * `trackexpr`: Used when working with an array of objects. The result of this expression will be
 *      used to identify the objects in the array. The `trackexpr` will most likely refer to the
 *     `value` variable (e.g. `value.propertyName`). With this the selection is preserved
 *      even when the options are recreated (e.g. reloaded from the server).
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} required The control is considered valid only if value is entered.
 * @param {string=} ngRequired Adds `required` attribute and `required` validation constraint to
 *    the element when the ngRequired expression evaluates to true. Use `ngRequired` instead of
 *    `required` when you want to data-bind to the `required` attribute.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 * @example
    <example module="selectExample" name="select">
      <file name="index.html">
        <script>
        angular.module('selectExample', [])
          .controller('ExampleController', ['$scope', function($scope) {
            $scope.colors = [
              {name:'black', shade:'dark'},
              {name:'white', shade:'light', notAnOption: true},
              {name:'red', shade:'dark'},
              {name:'blue', shade:'dark', notAnOption: true},
              {name:'yellow', shade:'light', notAnOption: false}
            ];
            $scope.myColor = $scope.colors[2]; // red
          }]);
        </script>
        <div ng-controller="ExampleController">
          <ul>
            <li ng-repeat="color in colors">
              <label>Name: <input ng-model="color.name"></label>
              <label><input type="checkbox" ng-model="color.notAnOption"> Disabled?</label>
              <button ng-click="colors.splice($index, 1)" aria-label="Remove">X</button>
            </li>
            <li>
              <button ng-click="colors.push({})">add</button>
            </li>
          </ul>
          <hr/>
          <label>Color (null not allowed):
            <select ng-model="myColor" ng-options="color.name for color in colors"></select>
          </label><br/>
          <label>Color (null allowed):
          <span  class="nullable">
            <select ng-model="myColor" ng-options="color.name for color in colors">
              <option value="">-- choose color --</option>
            </select>
          </span></label><br/>

          <label>Color grouped by shade:
            <select ng-model="myColor" ng-options="color.name group by color.shade for color in colors">
            </select>
          </label><br/>

          <label>Color grouped by shade, with some disabled:
            <select ng-model="myColor"
                  ng-options="color.name group by color.shade disable when color.notAnOption for color in colors">
            </select>
          </label><br/>



          Select <button ng-click="myColor = { name:'not in list', shade: 'other' }">bogus</button>.
          <br/>
          <hr/>
          Currently selected: {{ {selected_color:myColor} }}
          <div style="border:solid 1px black; height:20px"
               ng-style="{'background-color':myColor.name}">
          </div>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
         it('should check ng-options', function() {
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('red');
           element.all(by.model('myColor')).first().click();
           element.all(by.css('select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('black');
           element(by.css('.nullable select[ng-model="myColor"]')).click();
           element.all(by.css('.nullable select[ng-model="myColor"] option')).first().click();
           expect(element(by.binding('{selected_color:myColor}')).getText()).toMatch('null');
         });
      </file>
    </example>
 */

/* eslint-disable max-len */
//                     //00001111111111000000000002222222222000000000000000000000333333333300000000000000000000000004444444444400000000000005555555555555000000000666666666666600000007777777777777000000000000000888888888800000000000000000009999999999
var NG_OPTIONS_REGEXP = /^\s*([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+group\s+by\s+([\s\S]+?))?(?:\s+disable\s+when\s+([\s\S]+?))?\s+for\s+(?:([$\w][$\w]*)|(?:\(\s*([$\w][$\w]*)\s*,\s*([$\w][$\w]*)\s*\)))\s+in\s+([\s\S]+?)(?:\s+track\s+by\s+([\s\S]+?))?$/;
                        // 1: value expression (valueFn)
                        // 2: label expression (displayFn)
                        // 3: group by expression (groupByFn)
                        // 4: disable when expression (disableWhenFn)
                        // 5: array item variable name
                        // 6: object item key variable name
                        // 7: object item value variable name
                        // 8: collection expression
                        // 9: track by expression
/* eslint-enable */


var ngOptionsDirective = ['$compile', '$document', '$parse', function($compile, $document, $parse) {

  function parseOptionsExpression(optionsExp, selectElement, scope) {

    var match = optionsExp.match(NG_OPTIONS_REGEXP);
    if (!(match)) {
      throw ngOptionsMinErr('iexp',
        'Expected expression in form of ' +
        '\'_select_ (as _label_)? for (_key_,)?_value_ in _collection_\'' +
        ' but got \'{0}\'. Element: {1}',
        optionsExp, startingTag(selectElement));
    }

    // Extract the parts from the ngOptions expression

    // The variable name for the value of the item in the collection
    var valueName = match[5] || match[7];
    // The variable name for the key of the item in the collection
    var keyName = match[6];

    // An expression that generates the viewValue for an option if there is a label expression
    var selectAs = / as /.test(match[0]) && match[1];
    // An expression that is used to track the id of each object in the options collection
    var trackBy = match[9];
    // An expression that generates the viewValue for an option if there is no label expression
    var valueFn = $parse(match[2] ? match[1] : valueName);
    var selectAsFn = selectAs && $parse(selectAs);
    var viewValueFn = selectAsFn || valueFn;
    var trackByFn = trackBy && $parse(trackBy);

    // Get the value by which we are going to track the option
    // if we have a trackFn then use that (passing scope and locals)
    // otherwise just hash the given viewValue
    var getTrackByValueFn = trackBy ?
                              function(value, locals) { return trackByFn(scope, locals); } :
                              function getHashOfValue(value) { return hashKey(value); };
    var getTrackByValue = function(value, key) {
      return getTrackByValueFn(value, getLocals(value, key));
    };

    var displayFn = $parse(match[2] || match[1]);
    var groupByFn = $parse(match[3] || '');
    var disableWhenFn = $parse(match[4] || '');
    var valuesFn = $parse(match[8]);

    var locals = {};
    var getLocals = keyName ? function(value, key) {
      locals[keyName] = key;
      locals[valueName] = value;
      return locals;
    } : function(value) {
      locals[valueName] = value;
      return locals;
    };


    function Option(selectValue, viewValue, label, group, disabled) {
      this.selectValue = selectValue;
      this.viewValue = viewValue;
      this.label = label;
      this.group = group;
      this.disabled = disabled;
    }

    function getOptionValuesKeys(optionValues) {
      var optionValuesKeys;

      if (!keyName && isArrayLike(optionValues)) {
        optionValuesKeys = optionValues;
      } else {
        // if object, extract keys, in enumeration order, unsorted
        optionValuesKeys = [];
        for (var itemKey in optionValues) {
          if (optionValues.hasOwnProperty(itemKey) && itemKey.charAt(0) !== '$') {
            optionValuesKeys.push(itemKey);
          }
        }
      }
      return optionValuesKeys;
    }

    return {
      trackBy: trackBy,
      getTrackByValue: getTrackByValue,
      getWatchables: $parse(valuesFn, function(optionValues) {
        // Create a collection of things that we would like to watch (watchedArray)
        // so that they can all be watched using a single $watchCollection
        // that only runs the handler once if anything changes
        var watchedArray = [];
        optionValues = optionValues || [];

        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;
        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];

          var locals = getLocals(value, key);
          var selectValue = getTrackByValueFn(value, locals);
          watchedArray.push(selectValue);

          // Only need to watch the displayFn if there is a specific label expression
          if (match[2] || match[1]) {
            var label = displayFn(scope, locals);
            watchedArray.push(label);
          }

          // Only need to watch the disableWhenFn if there is a specific disable expression
          if (match[4]) {
            var disableWhen = disableWhenFn(scope, locals);
            watchedArray.push(disableWhen);
          }
        }
        return watchedArray;
      }),

      getOptions: function() {

        var optionItems = [];
        var selectValueMap = {};

        // The option values were already computed in the `getWatchables` fn,
        // which must have been called to trigger `getOptions`
        var optionValues = valuesFn(scope) || [];
        var optionValuesKeys = getOptionValuesKeys(optionValues);
        var optionValuesLength = optionValuesKeys.length;

        for (var index = 0; index < optionValuesLength; index++) {
          var key = (optionValues === optionValuesKeys) ? index : optionValuesKeys[index];
          var value = optionValues[key];
          var locals = getLocals(value, key);
          var viewValue = viewValueFn(scope, locals);
          var selectValue = getTrackByValueFn(viewValue, locals);
          var label = displayFn(scope, locals);
          var group = groupByFn(scope, locals);
          var disabled = disableWhenFn(scope, locals);
          var optionItem = new Option(selectValue, viewValue, label, group, disabled);

          optionItems.push(optionItem);
          selectValueMap[selectValue] = optionItem;
        }

        return {
          items: optionItems,
          selectValueMap: selectValueMap,
          getOptionFromViewValue: function(value) {
            return selectValueMap[getTrackByValue(value)];
          },
          getViewValueFromOption: function(option) {
            // If the viewValue could be an object that may be mutated by the application,
            // we need to make a copy and not return the reference to the value on the option.
            return trackBy ? copy(option.viewValue) : option.viewValue;
          }
        };
      }
    };
  }


  // Support: IE 9 only
  // We can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  var optionTemplate = window.document.createElement('option'),
      optGroupTemplate = window.document.createElement('optgroup');

    function ngOptionsPostLink(scope, selectElement, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];
      var multiple = attr.multiple;

      // The emptyOption allows the application developer to provide their own custom "empty"
      // option when the viewValue does not match any of the option values.
      for (var i = 0, children = selectElement.children(), ii = children.length; i < ii; i++) {
        if (children[i].value === '') {
          selectCtrl.hasEmptyOption = true;
          selectCtrl.emptyOption = children.eq(i);
          break;
        }
      }

      // The empty option will be compiled and rendered before we first generate the options
      selectElement.empty();

      var providedEmptyOption = !!selectCtrl.emptyOption;

      var unknownOption = jqLite(optionTemplate.cloneNode(false));
      unknownOption.val('?');

      var options;
      var ngOptions = parseOptionsExpression(attr.ngOptions, selectElement, scope);
      // This stores the newly created options before they are appended to the select.
      // Since the contents are removed from the fragment when it is appended,
      // we only need to create it once.
      var listFragment = $document[0].createDocumentFragment();

      // Overwrite the implementation. ngOptions doesn't use hashes
      selectCtrl.generateUnknownOptionValue = function(val) {
        return '?';
      };

      // Update the controller methods for multiple selectable options
      if (!multiple) {

        selectCtrl.writeValue = function writeNgOptionsValue(value) {
          // The options might not be defined yet when ngModel tries to render
          if (!options) return;

          var selectedOption = selectElement[0].options[selectElement[0].selectedIndex];
          var option = options.getOptionFromViewValue(value);

          // Make sure to remove the selected attribute from the previously selected option
          // Otherwise, screen readers might get confused
          if (selectedOption) selectedOption.removeAttribute('selected');

          if (option) {
            // Don't update the option when it is already selected.
            // For example, the browser will select the first option by default. In that case,
            // most properties are set automatically - except the `selected` attribute, which we
            // set always

            if (selectElement[0].value !== option.selectValue) {
              selectCtrl.removeUnknownOption();

              selectElement[0].value = option.selectValue;
              option.element.selected = true;
            }

            option.element.setAttribute('selected', 'selected');
          } else {
            selectCtrl.selectUnknownOrEmptyOption(value);
          }
        };

        selectCtrl.readValue = function readNgOptionsValue() {

          var selectedOption = options.selectValueMap[selectElement.val()];

          if (selectedOption && !selectedOption.disabled) {
            selectCtrl.unselectEmptyOption();
            selectCtrl.removeUnknownOption();
            return options.getViewValueFromOption(selectedOption);
          }
          return null;
        };

        // If we are using `track by` then we must watch the tracked value on the model
        // since ngModel only watches for object identity change
        // FIXME: When a user selects an option, this watch will fire needlessly
        if (ngOptions.trackBy) {
          scope.$watch(
            function() { return ngOptions.getTrackByValue(ngModelCtrl.$viewValue); },
            function() { ngModelCtrl.$render(); }
          );
        }

      } else {

        selectCtrl.writeValue = function writeNgOptionsMultiple(values) {
          // The options might not be defined yet when ngModel tries to render
          if (!options) return;

          // Only set `<option>.selected` if necessary, in order to prevent some browsers from
          // scrolling to `<option>` elements that are outside the `<select>` element's viewport.
          var selectedOptions = values && values.map(getAndUpdateSelectedOption) || [];

          options.items.forEach(function(option) {
            if (option.element.selected && !includes(selectedOptions, option)) {
              option.element.selected = false;
            }
          });
        };


        selectCtrl.readValue = function readNgOptionsMultiple() {
          var selectedValues = selectElement.val() || [],
              selections = [];

          forEach(selectedValues, function(value) {
            var option = options.selectValueMap[value];
            if (option && !option.disabled) selections.push(options.getViewValueFromOption(option));
          });

          return selections;
        };

        // If we are using `track by` then we must watch these tracked values on the model
        // since ngModel only watches for object identity change
        if (ngOptions.trackBy) {

          scope.$watchCollection(function() {
            if (isArray(ngModelCtrl.$viewValue)) {
              return ngModelCtrl.$viewValue.map(function(value) {
                return ngOptions.getTrackByValue(value);
              });
            }
          }, function() {
            ngModelCtrl.$render();
          });

        }
      }

      if (providedEmptyOption) {

        // compile the element since there might be bindings in it
        $compile(selectCtrl.emptyOption)(scope);

        selectElement.prepend(selectCtrl.emptyOption);

        if (selectCtrl.emptyOption[0].nodeType === NODE_TYPE_COMMENT) {
          // This means the empty option has currently no actual DOM node, probably because
          // it has been modified by a transclusion directive.
          selectCtrl.hasEmptyOption = false;

          // Redefine the registerOption function, which will catch
          // options that are added by ngIf etc. (rendering of the node is async because of
          // lazy transclusion)
          selectCtrl.registerOption = function(optionScope, optionEl) {
            if (optionEl.val() === '') {
              selectCtrl.hasEmptyOption = true;
              selectCtrl.emptyOption = optionEl;
              selectCtrl.emptyOption.removeClass('ng-scope');
              // This ensures the new empty option is selected if previously no option was selected
              ngModelCtrl.$render();

              optionEl.on('$destroy', function() {
                var needsRerender = selectCtrl.$isEmptyOptionSelected();

                selectCtrl.hasEmptyOption = false;
                selectCtrl.emptyOption = undefined;

                if (needsRerender) ngModelCtrl.$render();
              });
            }
          };

        } else {
          // remove the class, which is added automatically because we recompile the element and it
          // becomes the compilation root
          selectCtrl.emptyOption.removeClass('ng-scope');
        }

      }

      // We will re-render the option elements if the option values or labels change
      scope.$watchCollection(ngOptions.getWatchables, updateOptions);

      // ------------------------------------------------------------------ //

      function addOptionElement(option, parent) {
        var optionElement = optionTemplate.cloneNode(false);
        parent.appendChild(optionElement);
        updateOptionElement(option, optionElement);
      }

      function getAndUpdateSelectedOption(viewValue) {
        var option = options.getOptionFromViewValue(viewValue);
        var element = option && option.element;

        if (element && !element.selected) element.selected = true;

        return option;
      }

      function updateOptionElement(option, element) {
        option.element = element;
        element.disabled = option.disabled;
        // Support: IE 11 only, Edge 12-13 only
        // NOTE: The label must be set before the value, otherwise IE 11 & Edge create unresponsive
        // selects in certain circumstances when multiple selects are next to each other and display
        // the option list in listbox style, i.e. the select is [multiple], or specifies a [size].
        // See https://github.com/angular/angular.js/issues/11314 for more info.
        // This is unfortunately untestable with unit / e2e tests
        if (option.label !== element.label) {
          element.label = option.label;
          element.textContent = option.label;
        }
        element.value = option.selectValue;
      }

      function updateOptions() {
        var previousValue = options && selectCtrl.readValue();

        // We must remove all current options, but cannot simply set innerHTML = null
        // since the providedEmptyOption might have an ngIf on it that inserts comments which we
        // must preserve.
        // Instead, iterate over the current option elements and remove them or their optgroup
        // parents
        if (options) {

          for (var i = options.items.length - 1; i >= 0; i--) {
            var option = options.items[i];
            if (isDefined(option.group)) {
              jqLiteRemove(option.element.parentNode);
            } else {
              jqLiteRemove(option.element);
            }
          }
        }

        options = ngOptions.getOptions();

        var groupElementMap = {};

        options.items.forEach(function addOption(option) {
          var groupElement;

          if (isDefined(option.group)) {

            // This option is to live in a group
            // See if we have already created this group
            groupElement = groupElementMap[option.group];

            if (!groupElement) {

              groupElement = optGroupTemplate.cloneNode(false);
              listFragment.appendChild(groupElement);

              // Update the label on the group element
              // "null" is special cased because of Safari
              groupElement.label = option.group === null ? 'null' : option.group;

              // Store it for use later
              groupElementMap[option.group] = groupElement;
            }

            addOptionElement(option, groupElement);

          } else {

            // This option is not in a group
            addOptionElement(option, listFragment);
          }
        });

        selectElement[0].appendChild(listFragment);

        ngModelCtrl.$render();

        // Check to see if the value has changed due to the update to the options
        if (!ngModelCtrl.$isEmpty(previousValue)) {
          var nextValue = selectCtrl.readValue();
          var isNotPrimitive = ngOptions.trackBy || multiple;
          if (isNotPrimitive ? !equals(previousValue, nextValue) : previousValue !== nextValue) {
            ngModelCtrl.$setViewValue(nextValue);
            ngModelCtrl.$render();
          }
        }
      }
  }

  return {
    restrict: 'A',
    terminal: true,
    require: ['select', 'ngModel'],
    link: {
      pre: function ngOptionsPreLink(scope, selectElement, attr, ctrls) {
        // Deactivate the SelectController.register method to prevent
        // option directives from accidentally registering themselves
        // (and unwanted $destroy handlers etc.)
        ctrls[0].registerOption = noop;
      },
      post: ngOptionsPostLink
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngPluralize
 * @restrict EA
 *
 * @description
 * `ngPluralize` is a directive that displays messages according to en-US localization rules.
 * These rules are bundled with angular.js, but can be overridden
 * (see {@link guide/i18n Angular i18n} dev guide). You configure ngPluralize directive
 * by specifying the mappings between
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * and the strings to be displayed.
 *
 * # Plural categories and explicit number rules
 * There are two
 * [plural categories](http://unicode.org/repos/cldr-tmp/trunk/diff/supplemental/language_plural_rules.html)
 * in Angular's default en-US locale: "one" and "other".
 *
 * While a plural category may match many numbers (for example, in en-US locale, "other" can match
 * any number that is not 1), an explicit number rule can only match one number. For example, the
 * explicit number rule for "3" matches the number 3. There are examples of plural categories
 * and explicit number rules throughout the rest of this documentation.
 *
 * # Configuring ngPluralize
 * You configure ngPluralize by providing 2 attributes: `count` and `when`.
 * You can also provide an optional attribute, `offset`.
 *
 * The value of the `count` attribute can be either a string or an {@link guide/expression
 * Angular expression}; these are evaluated on the current scope for its bound value.
 *
 * The `when` attribute specifies the mappings between plural categories and the actual
 * string to be displayed. The value of the attribute should be a JSON object.
 *
 * The following example shows how to configure ngPluralize:
 *
 * ```html
 * <ng-pluralize count="personCount"
                 when="{'0': 'Nobody is viewing.',
 *                      'one': '1 person is viewing.',
 *                      'other': '{} people are viewing.'}">
 * </ng-pluralize>
 *```
 *
 * In the example, `"0: Nobody is viewing."` is an explicit number rule. If you did not
 * specify this rule, 0 would be matched to the "other" category and "0 people are viewing"
 * would be shown instead of "Nobody is viewing". You can specify an explicit number rule for
 * other numbers, for example 12, so that instead of showing "12 people are viewing", you can
 * show "a dozen people are viewing".
 *
 * You can use a set of closed braces (`{}`) as a placeholder for the number that you want substituted
 * into pluralized strings. In the previous example, Angular will replace `{}` with
 * <span ng-non-bindable>`{{personCount}}`</span>. The closed braces `{}` is a placeholder
 * for <span ng-non-bindable>{{numberExpression}}</span>.
 *
 * If no rule is defined for a category, then an empty string is displayed and a warning is generated.
 * Note that some locales define more categories than `one` and `other`. For example, fr-fr defines `few` and `many`.
 *
 * # Configuring ngPluralize with offset
 * The `offset` attribute allows further customization of pluralized text, which can result in
 * a better user experience. For example, instead of the message "4 people are viewing this document",
 * you might display "John, Kate and 2 others are viewing this document".
 * The offset attribute allows you to offset a number by any desired value.
 * Let's take a look at an example:
 *
 * ```html
 * <ng-pluralize count="personCount" offset=2
 *               when="{'0': 'Nobody is viewing.',
 *                      '1': '{{person1}} is viewing.',
 *                      '2': '{{person1}} and {{person2}} are viewing.',
 *                      'one': '{{person1}}, {{person2}} and one other person are viewing.',
 *                      'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
 * </ng-pluralize>
 * ```
 *
 * Notice that we are still using two plural categories(one, other), but we added
 * three explicit number rules 0, 1 and 2.
 * When one person, perhaps John, views the document, "John is viewing" will be shown.
 * When three people view the document, no explicit number rule is found, so
 * an offset of 2 is taken off 3, and Angular uses 1 to decide the plural category.
 * In this case, plural category 'one' is matched and "John, Mary and one other person are viewing"
 * is shown.
 *
 * Note that when you specify offsets, you must provide explicit number rules for
 * numbers from 0 up to and including the offset. If you use an offset of 3, for example,
 * you must provide explicit number rules for 0, 1, 2 and 3. You must also provide plural strings for
 * plural categories "one" and "other".
 *
 * @param {string|expression} count The variable to be bound to.
 * @param {string} when The mapping between plural category to its corresponding strings.
 * @param {number=} offset Offset to deduct from the total number.
 *
 * @example
    <example module="pluralizeExample" name="ng-pluralize">
      <file name="index.html">
        <script>
          angular.module('pluralizeExample', [])
            .controller('ExampleController', ['$scope', function($scope) {
              $scope.person1 = 'Igor';
              $scope.person2 = 'Misko';
              $scope.personCount = 1;
            }]);
        </script>
        <div ng-controller="ExampleController">
          <label>Person 1:<input type="text" ng-model="person1" value="Igor" /></label><br/>
          <label>Person 2:<input type="text" ng-model="person2" value="Misko" /></label><br/>
          <label>Number of People:<input type="text" ng-model="personCount" value="1" /></label><br/>

          <!--- Example with simple pluralization rules for en locale --->
          Without Offset:
          <ng-pluralize count="personCount"
                        when="{'0': 'Nobody is viewing.',
                               'one': '1 person is viewing.',
                               'other': '{} people are viewing.'}">
          </ng-pluralize><br>

          <!--- Example with offset --->
          With Offset(2):
          <ng-pluralize count="personCount" offset=2
                        when="{'0': 'Nobody is viewing.',
                               '1': '{{person1}} is viewing.',
                               '2': '{{person1}} and {{person2}} are viewing.',
                               'one': '{{person1}}, {{person2}} and one other person are viewing.',
                               'other': '{{person1}}, {{person2}} and {} other people are viewing.'}">
          </ng-pluralize>
        </div>
      </file>
      <file name="protractor.js" type="protractor">
        it('should show correct pluralized string', function() {
          var withoutOffset = element.all(by.css('ng-pluralize')).get(0);
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var countInput = element(by.model('personCount'));

          expect(withoutOffset.getText()).toEqual('1 person is viewing.');
          expect(withOffset.getText()).toEqual('Igor is viewing.');

          countInput.clear();
          countInput.sendKeys('0');

          expect(withoutOffset.getText()).toEqual('Nobody is viewing.');
          expect(withOffset.getText()).toEqual('Nobody is viewing.');

          countInput.clear();
          countInput.sendKeys('2');

          expect(withoutOffset.getText()).toEqual('2 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor and Misko are viewing.');

          countInput.clear();
          countInput.sendKeys('3');

          expect(withoutOffset.getText()).toEqual('3 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and one other person are viewing.');

          countInput.clear();
          countInput.sendKeys('4');

          expect(withoutOffset.getText()).toEqual('4 people are viewing.');
          expect(withOffset.getText()).toEqual('Igor, Misko and 2 other people are viewing.');
        });
        it('should show data-bound names', function() {
          var withOffset = element.all(by.css('ng-pluralize')).get(1);
          var personCount = element(by.model('personCount'));
          var person1 = element(by.model('person1'));
          var person2 = element(by.model('person2'));
          personCount.clear();
          personCount.sendKeys('4');
          person1.clear();
          person1.sendKeys('Di');
          person2.clear();
          person2.sendKeys('Vojta');
          expect(withOffset.getText()).toEqual('Di, Vojta and 2 other people are viewing.');
        });
      </file>
    </example>
 */
var ngPluralizeDirective = ['$locale', '$interpolate', '$log', function($locale, $interpolate, $log) {
  var BRACE = /{}/g,
      IS_WHEN = /^when(Minus)?(.+)$/;

  return {
    link: function(scope, element, attr) {
      var numberExp = attr.count,
          whenExp = attr.$attr.when && element.attr(attr.$attr.when), // we have {{}} in attrs
          offset = attr.offset || 0,
          whens = scope.$eval(whenExp) || {},
          whensExpFns = {},
          startSymbol = $interpolate.startSymbol(),
          endSymbol = $interpolate.endSymbol(),
          braceReplacement = startSymbol + numberExp + '-' + offset + endSymbol,
          watchRemover = angular.noop,
          lastCount;

      forEach(attr, function(expression, attributeName) {
        var tmpMatch = IS_WHEN.exec(attributeName);
        if (tmpMatch) {
          var whenKey = (tmpMatch[1] ? '-' : '') + lowercase(tmpMatch[2]);
          whens[whenKey] = element.attr(attr.$attr[attributeName]);
        }
      });
      forEach(whens, function(expression, key) {
        whensExpFns[key] = $interpolate(expression.replace(BRACE, braceReplacement));

      });

      scope.$watch(numberExp, function ngPluralizeWatchAction(newVal) {
        var count = parseFloat(newVal);
        var countIsNaN = isNumberNaN(count);

        if (!countIsNaN && !(count in whens)) {
          // If an explicit number rule such as 1, 2, 3... is defined, just use it.
          // Otherwise, check it against pluralization rules in $locale service.
          count = $locale.pluralCat(count - offset);
        }

        // If both `count` and `lastCount` are NaN, we don't need to re-register a watch.
        // In JS `NaN !== NaN`, so we have to explicitly check.
        if ((count !== lastCount) && !(countIsNaN && isNumberNaN(lastCount))) {
          watchRemover();
          var whenExpFn = whensExpFns[count];
          if (isUndefined(whenExpFn)) {
            if (newVal != null) {
              $log.debug('ngPluralize: no rule defined for \'' + count + '\' in ' + whenExp);
            }
            watchRemover = noop;
            updateElementText();
          } else {
            watchRemover = scope.$watch(whenExpFn, updateElementText);
          }
          lastCount = count;
        }
      });

      function updateElementText(newText) {
        element.text(newText || '');
      }
    }
  };
}];

/* exported ngRepeatDirective */

/**
 * @ngdoc directive
 * @name ngRepeat
 * @multiElement
 * @restrict A
 *
 * @description
 * The `ngRepeat` directive instantiates a template once per item from a collection. Each template
 * instance gets its own scope, where the given loop variable is set to the current collection item,
 * and `$index` is set to the item index or key.
 *
 * Special properties are exposed on the local scope of each template instance, including:
 *
 * | Variable  | Type            | Details                                                                     |
 * |-----------|-----------------|-----------------------------------------------------------------------------|
 * | `$index`  | {@type number}  | iterator offset of the repeated element (0..length-1)                       |
 * | `$first`  | {@type boolean} | true if the repeated element is first in the iterator.                      |
 * | `$middle` | {@type boolean} | true if the repeated element is between the first and last in the iterator. |
 * | `$last`   | {@type boolean} | true if the repeated element is last in the iterator.                       |
 * | `$even`   | {@type boolean} | true if the iterator position `$index` is even (otherwise false).           |
 * | `$odd`    | {@type boolean} | true if the iterator position `$index` is odd (otherwise false).            |
 *
 * <div class="alert alert-info">
 *   Creating aliases for these properties is possible with {@link ng.directive:ngInit `ngInit`}.
 *   This may be useful when, for instance, nesting ngRepeats.
 * </div>
 *
 *
 * # Iterating over object properties
 *
 * It is possible to get `ngRepeat` to iterate over the properties of an object using the following
 * syntax:
 *
 * ```js
 * <div ng-repeat="(key, value) in myObj"> ... </div>
 * ```
 *
 * However, there are a few limitations compared to array iteration:
 *
 * - The JavaScript specification does not define the order of keys
 *   returned for an object, so Angular relies on the order returned by the browser
 *   when running `for key in myObj`. Browsers generally follow the strategy of providing
 *   keys in the order in which they were defined, although there are exceptions when keys are deleted
 *   and reinstated. See the
 *   [MDN page on `delete` for more info](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/delete#Cross-browser_notes).
 *
 * - `ngRepeat` will silently *ignore* object keys starting with `$`, because
 *   it's a prefix used by Angular for public (`$`) and private (`$$`) properties.
 *
 * - The built-in filters {@link ng.orderBy orderBy} and {@link ng.filter filter} do not work with
 *   objects, and will throw an error if used with one.
 *
 * If you are hitting any of these limitations, the recommended workaround is to convert your object into an array
 * that is sorted into the order that you prefer before providing it to `ngRepeat`. You could
 * do this with a filter such as [toArrayFilter](http://ngmodules.org/modules/angular-toArrayFilter)
 * or implement a `$watch` on the object yourself.
 *
 *
 * # Tracking and Duplicates
 *
 * `ngRepeat` uses {@link $rootScope.Scope#$watchCollection $watchCollection} to detect changes in
 * the collection. When a change happens, `ngRepeat` then makes the corresponding changes to the DOM:
 *
 * * When an item is added, a new instance of the template is added to the DOM.
 * * When an item is removed, its template instance is removed from the DOM.
 * * When items are reordered, their respective templates are reordered in the DOM.
 *
 * To minimize creation of DOM elements, `ngRepeat` uses a function
 * to "keep track" of all items in the collection and their corresponding DOM elements.
 * For example, if an item is added to the collection, `ngRepeat` will know that all other items
 * already have DOM elements, and will not re-render them.
 *
 * The default tracking function (which tracks items by their identity) does not allow
 * duplicate items in arrays. This is because when there are duplicates, it is not possible
 * to maintain a one-to-one mapping between collection items and DOM elements.
 *
 * If you do need to repeat duplicate items, you can substitute the default tracking behavior
 * with your own using the `track by` expression.
 *
 * For example, you may track items by the index of each item in the collection, using the
 * special scope property `$index`:
 * ```html
 *    <div ng-repeat="n in [42, 42, 43, 43] track by $index">
 *      {{n}}
 *    </div>
 * ```
 *
 * You may also use arbitrary expressions in `track by`, including references to custom functions
 * on the scope:
 * ```html
 *    <div ng-repeat="n in [42, 42, 43, 43] track by myTrackingFunction(n)">
 *      {{n}}
 *    </div>
 * ```
 *
 * <div class="alert alert-success">
 * If you are working with objects that have a unique identifier property, you should track
 * by this identifier instead of the object instance. Should you reload your data later, `ngRepeat`
 * will not have to rebuild the DOM elements for items it has already rendered, even if the
 * JavaScript objects in the collection have been substituted for new ones. For large collections,
 * this significantly improves rendering performance. If you don't have a unique identifier,
 * `track by $index` can also provide a performance boost.
 * </div>
 *
 * ```html
 *    <div ng-repeat="model in collection track by model.id">
 *      {{model.name}}
 *    </div>
 * ```
 *
 * <br />
 * <div class="alert alert-warning">
 * Avoid using `track by $index` when the repeated template contains
 * {@link guide/expression#one-time-binding one-time bindings}. In such cases, the `nth` DOM
 * element will always be matched with the `nth` item of the array, so the bindings on that element
 * will not be updated even when the corresponding item changes, essentially causing the view to get
 * out-of-sync with the underlying data.
 * </div>
 *
 * When no `track by` expression is provided, it is equivalent to tracking by the built-in
 * `$id` function, which tracks items by their identity:
 * ```html
 *    <div ng-repeat="obj in collection track by $id(obj)">
 *      {{obj.prop}}
 *    </div>
 * ```
 *
 * <br />
 * <div class="alert alert-warning">
 * **Note:** `track by` must always be the last expression:
 * </div>
 * ```
 *    <div ng-repeat="model in collection | orderBy: 'id' as filtered_result track by model.id">
 *      {{model.name}}
 *    </div>
 * ```
 *
 *
 * # Special repeat start and end points
 * To repeat a series of elements instead of just one parent element, ngRepeat (as well as other ng directives) supports extending
 * the range of the repeater by defining explicit start and end points by using **ng-repeat-start** and **ng-repeat-end** respectively.
 * The **ng-repeat-start** directive works the same as **ng-repeat**, but will repeat all the HTML code (including the tag it's defined on)
 * up to and including the ending HTML tag where **ng-repeat-end** is placed.
 *
 * The example below makes use of this feature:
 * ```html
 *   <header ng-repeat-start="item in items">
 *     Header {{ item }}
 *   </header>
 *   <div class="body">
 *     Body {{ item }}
 *   </div>
 *   <footer ng-repeat-end>
 *     Footer {{ item }}
 *   </footer>
 * ```
 *
 * And with an input of {@type ['A','B']} for the items variable in the example above, the output will evaluate to:
 * ```html
 *   <header>
 *     Header A
 *   </header>
 *   <div class="body">
 *     Body A
 *   </div>
 *   <footer>
 *     Footer A
 *   </footer>
 *   <header>
 *     Header B
 *   </header>
 *   <div class="body">
 *     Body B
 *   </div>
 *   <footer>
 *     Footer B
 *   </footer>
 * ```
 *
 * The custom start and end points for ngRepeat also support all other HTML directive syntax flavors provided in AngularJS (such
 * as **data-ng-repeat-start**, **x-ng-repeat-start** and **ng:repeat-start**).
 *
 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter} | when a new item is added to the list or when an item is revealed after a filter |
 * | {@link ng.$animate#leave leave} | when an item is removed from the list or when an item is filtered out |
 * | {@link ng.$animate#move move } | when an adjacent item is filtered out causing a reorder or when the item contents are reordered |
 *
 * See the example below for defining CSS animations with ngRepeat.
 *
 * @element ANY
 * @scope
 * @priority 1000
 * @param {repeat_expression} ngRepeat The expression indicating how to enumerate a collection. These
 *   formats are currently supported:
 *
 *   * `variable in expression` – where variable is the user defined loop variable and `expression`
 *     is a scope expression giving the collection to enumerate.
 *
 *     For example: `album in artist.albums`.
 *
 *   * `(key, value) in expression` – where `key` and `value` can be any user defined identifiers,
 *     and `expression` is the scope expression giving the collection to enumerate.
 *
 *     For example: `(name, age) in {'adam':10, 'amalie':12}`.
 *
 *   * `variable in expression track by tracking_expression` – You can also provide an optional tracking expression
 *     which can be used to associate the objects in the collection with the DOM elements. If no tracking expression
 *     is specified, ng-repeat associates elements by identity. It is an error to have
 *     more than one tracking expression value resolve to the same key. (This would mean that two distinct objects are
 *     mapped to the same DOM element, which is not possible.)
 *
 *     <div class="alert alert-warning">
 *       <strong>Note:</strong> the `track by` expression must come last - after any filters, and the alias expression.
 *     </div>
 *
 *     For example: `item in items` is equivalent to `item in items track by $id(item)`. This implies that the DOM elements
 *     will be associated by item identity in the array.
 *
 *     For example: `item in items track by $id(item)`. A built in `$id()` function can be used to assign a unique
 *     `$$hashKey` property to each item in the array. This property is then used as a key to associated DOM elements
 *     with the corresponding item in the array by identity. Moving the same object in array would move the DOM
 *     element in the same way in the DOM.
 *
 *     For example: `item in items track by item.id` is a typical pattern when the items come from the database. In this
 *     case the object identity does not matter. Two objects are considered equivalent as long as their `id`
 *     property is same.
 *
 *     For example: `item in items | filter:searchText track by item.id` is a pattern that might be used to apply a filter
 *     to items in conjunction with a tracking expression.
 *
 *   * `variable in expression as alias_expression` – You can also provide an optional alias expression which will then store the
 *     intermediate results of the repeater after the filters have been applied. Typically this is used to render a special message
 *     when a filter is active on the repeater, but the filtered result set is empty.
 *
 *     For example: `item in items | filter:x as results` will store the fragment of the repeated items as `results`, but only after
 *     the items have been processed through the filter.
 *
 *     Please note that `as [variable name] is not an operator but rather a part of ngRepeat micro-syntax so it can be used only at the end
 *     (and not as operator, inside an expression).
 *
 *     For example: `item in items | filter : x | orderBy : order | limitTo : limit as results` .
 *
 * @example
 * This example uses `ngRepeat` to display a list of people. A filter is used to restrict the displayed
 * results by name or by age. New (entering) and removed (leaving) items are animated.
  <example module="ngRepeat" name="ngRepeat" deps="angular-animate.js" animations="true" name="ng-repeat">
    <file name="index.html">
      <div ng-controller="repeatController">
        I have {{friends.length}} friends. They are:
        <input type="search" ng-model="q" placeholder="filter friends..." aria-label="filter friends" />
        <ul class="example-animate-container">
          <li class="animate-repeat" ng-repeat="friend in friends | filter:q as results">
            [{{$index + 1}}] {{friend.name}} who is {{friend.age}} years old.
          </li>
          <li class="animate-repeat" ng-if="results.length === 0">
            <strong>No results found...</strong>
          </li>
        </ul>
      </div>
    </file>
    <file name="script.js">
      angular.module('ngRepeat', ['ngAnimate']).controller('repeatController', function($scope) {
        $scope.friends = [
          {name:'John', age:25, gender:'boy'},
          {name:'Jessie', age:30, gender:'girl'},
          {name:'Johanna', age:28, gender:'girl'},
          {name:'Joy', age:15, gender:'girl'},
          {name:'Mary', age:28, gender:'girl'},
          {name:'Peter', age:95, gender:'boy'},
          {name:'Sebastian', age:50, gender:'boy'},
          {name:'Erika', age:27, gender:'girl'},
          {name:'Patrick', age:40, gender:'boy'},
          {name:'Samantha', age:60, gender:'girl'}
        ];
      });
    </file>
    <file name="animations.css">
      .example-animate-container {
        background:white;
        border:1px solid black;
        list-style:none;
        margin:0;
        padding:0 10px;
      }

      .animate-repeat {
        line-height:30px;
        list-style:none;
        box-sizing:border-box;
      }

      .animate-repeat.ng-move,
      .animate-repeat.ng-enter,
      .animate-repeat.ng-leave {
        transition:all linear 0.5s;
      }

      .animate-repeat.ng-leave.ng-leave-active,
      .animate-repeat.ng-move,
      .animate-repeat.ng-enter {
        opacity:0;
        max-height:0;
      }

      .animate-repeat.ng-leave,
      .animate-repeat.ng-move.ng-move-active,
      .animate-repeat.ng-enter.ng-enter-active {
        opacity:1;
        max-height:30px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var friends = element.all(by.repeater('friend in friends'));

      it('should render initial data set', function() {
        expect(friends.count()).toBe(10);
        expect(friends.get(0).getText()).toEqual('[1] John who is 25 years old.');
        expect(friends.get(1).getText()).toEqual('[2] Jessie who is 30 years old.');
        expect(friends.last().getText()).toEqual('[10] Samantha who is 60 years old.');
        expect(element(by.binding('friends.length')).getText())
            .toMatch("I have 10 friends. They are:");
      });

       it('should update repeater when filter predicate changes', function() {
         expect(friends.count()).toBe(10);

         element(by.model('q')).sendKeys('ma');

         expect(friends.count()).toBe(2);
         expect(friends.get(0).getText()).toEqual('[1] Mary who is 28 years old.');
         expect(friends.last().getText()).toEqual('[2] Samantha who is 60 years old.');
       });
      </file>
    </example>
 */
var ngRepeatDirective = ['$parse', '$animate', '$compile', function($parse, $animate, $compile) {
  var NG_REMOVED = '$$NG_REMOVED';
  var ngRepeatMinErr = minErr('ngRepeat');

  var updateScope = function(scope, index, valueIdentifier, value, keyIdentifier, key, arrayLength) {
    // TODO(perf): generate setters to shave off ~40ms or 1-1.5%
    scope[valueIdentifier] = value;
    if (keyIdentifier) scope[keyIdentifier] = key;
    scope.$index = index;
    scope.$first = (index === 0);
    scope.$last = (index === (arrayLength - 1));
    scope.$middle = !(scope.$first || scope.$last);
    // eslint-disable-next-line no-bitwise
    scope.$odd = !(scope.$even = (index & 1) === 0);
  };

  var getBlockStart = function(block) {
    return block.clone[0];
  };

  var getBlockEnd = function(block) {
    return block.clone[block.clone.length - 1];
  };


  return {
    restrict: 'A',
    multiElement: true,
    transclude: 'element',
    priority: 1000,
    terminal: true,
    $$tlb: true,
    compile: function ngRepeatCompile($element, $attr) {
      var expression = $attr.ngRepeat;
      var ngRepeatEndComment = $compile.$$createComment('end ngRepeat', expression);

      var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)(?:\s+as\s+([\s\S]+?))?(?:\s+track\s+by\s+([\s\S]+?))?\s*$/);

      if (!match) {
        throw ngRepeatMinErr('iexp', 'Expected expression in form of \'_item_ in _collection_[ track by _id_]\' but got \'{0}\'.',
            expression);
      }

      var lhs = match[1];
      var rhs = match[2];
      var aliasAs = match[3];
      var trackByExp = match[4];

      match = lhs.match(/^(?:(\s*[$\w]+)|\(\s*([$\w]+)\s*,\s*([$\w]+)\s*\))$/);

      if (!match) {
        throw ngRepeatMinErr('iidexp', '\'_item_\' in \'_item_ in _collection_\' should be an identifier or \'(_key_, _value_)\' expression, but got \'{0}\'.',
            lhs);
      }
      var valueIdentifier = match[3] || match[1];
      var keyIdentifier = match[2];

      if (aliasAs && (!/^[$a-zA-Z_][$a-zA-Z0-9_]*$/.test(aliasAs) ||
          /^(null|undefined|this|\$index|\$first|\$middle|\$last|\$even|\$odd|\$parent|\$root|\$id)$/.test(aliasAs))) {
        throw ngRepeatMinErr('badident', 'alias \'{0}\' is invalid --- must be a valid JS identifier which is not a reserved name.',
          aliasAs);
      }

      var trackByExpGetter, trackByIdExpFn, trackByIdArrayFn, trackByIdObjFn;
      var hashFnLocals = {$id: hashKey};

      if (trackByExp) {
        trackByExpGetter = $parse(trackByExp);
      } else {
        trackByIdArrayFn = function(key, value) {
          return hashKey(value);
        };
        trackByIdObjFn = function(key) {
          return key;
        };
      }

      return function ngRepeatLink($scope, $element, $attr, ctrl, $transclude) {

        if (trackByExpGetter) {
          trackByIdExpFn = function(key, value, index) {
            // assign key, value, and $index to the locals so that they can be used in hash functions
            if (keyIdentifier) hashFnLocals[keyIdentifier] = key;
            hashFnLocals[valueIdentifier] = value;
            hashFnLocals.$index = index;
            return trackByExpGetter($scope, hashFnLocals);
          };
        }

        // Store a list of elements from previous run. This is a hash where key is the item from the
        // iterator, and the value is objects with following properties.
        //   - scope: bound scope
        //   - clone: previous element.
        //   - index: position
        //
        // We are using no-proto object so that we don't need to guard against inherited props via
        // hasOwnProperty.
        var lastBlockMap = createMap();

        //watch props
        $scope.$watchCollection(rhs, function ngRepeatAction(collection) {
          var index, length,
              previousNode = $element[0],     // node that cloned nodes should be inserted after
                                              // initialized to the comment node anchor
              nextNode,
              // Same as lastBlockMap but it has the current state. It will become the
              // lastBlockMap on the next iteration.
              nextBlockMap = createMap(),
              collectionLength,
              key, value, // key/value of iteration
              trackById,
              trackByIdFn,
              collectionKeys,
              block,       // last object information {scope, element, id}
              nextBlockOrder,
              elementsToRemove;

          if (aliasAs) {
            $scope[aliasAs] = collection;
          }

          if (isArrayLike(collection)) {
            collectionKeys = collection;
            trackByIdFn = trackByIdExpFn || trackByIdArrayFn;
          } else {
            trackByIdFn = trackByIdExpFn || trackByIdObjFn;
            // if object, extract keys, in enumeration order, unsorted
            collectionKeys = [];
            for (var itemKey in collection) {
              if (hasOwnProperty.call(collection, itemKey) && itemKey.charAt(0) !== '$') {
                collectionKeys.push(itemKey);
              }
            }
          }

          collectionLength = collectionKeys.length;
          nextBlockOrder = new Array(collectionLength);

          // locate existing items
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            trackById = trackByIdFn(key, value, index);
            if (lastBlockMap[trackById]) {
              // found previously seen block
              block = lastBlockMap[trackById];
              delete lastBlockMap[trackById];
              nextBlockMap[trackById] = block;
              nextBlockOrder[index] = block;
            } else if (nextBlockMap[trackById]) {
              // if collision detected. restore lastBlockMap and throw an error
              forEach(nextBlockOrder, function(block) {
                if (block && block.scope) lastBlockMap[block.id] = block;
              });
              throw ngRepeatMinErr('dupes',
                  'Duplicates in a repeater are not allowed. Use \'track by\' expression to specify unique keys. Repeater: {0}, Duplicate key: {1}, Duplicate value: {2}',
                  expression, trackById, value);
            } else {
              // new never before seen block
              nextBlockOrder[index] = {id: trackById, scope: undefined, clone: undefined};
              nextBlockMap[trackById] = true;
            }
          }

          // remove leftover items
          for (var blockKey in lastBlockMap) {
            block = lastBlockMap[blockKey];
            elementsToRemove = getBlockNodes(block.clone);
            $animate.leave(elementsToRemove);
            if (elementsToRemove[0].parentNode) {
              // if the element was not removed yet because of pending animation, mark it as deleted
              // so that we can ignore it later
              for (index = 0, length = elementsToRemove.length; index < length; index++) {
                elementsToRemove[index][NG_REMOVED] = true;
              }
            }
            block.scope.$destroy();
          }

          // we are not using forEach for perf reasons (trying to avoid #call)
          for (index = 0; index < collectionLength; index++) {
            key = (collection === collectionKeys) ? index : collectionKeys[index];
            value = collection[key];
            block = nextBlockOrder[index];

            if (block.scope) {
              // if we have already seen this object, then we need to reuse the
              // associated scope/element

              nextNode = previousNode;

              // skip nodes that are already pending removal via leave animation
              do {
                nextNode = nextNode.nextSibling;
              } while (nextNode && nextNode[NG_REMOVED]);

              if (getBlockStart(block) !== nextNode) {
                // existing item which got moved
                $animate.move(getBlockNodes(block.clone), null, previousNode);
              }
              previousNode = getBlockEnd(block);
              updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
            } else {
              // new item which we don't know about
              $transclude(function ngRepeatTransclude(clone, scope) {
                block.scope = scope;
                // http://jsperf.com/clone-vs-createcomment
                var endNode = ngRepeatEndComment.cloneNode(false);
                clone[clone.length++] = endNode;

                $animate.enter(clone, null, previousNode);
                previousNode = endNode;
                // Note: We only need the first/last node of the cloned nodes.
                // However, we need to keep the reference to the jqlite wrapper as it might be changed later
                // by a directive with templateUrl when its template arrives.
                block.clone = clone;
                nextBlockMap[block.id] = block;
                updateScope(block.scope, index, valueIdentifier, value, keyIdentifier, key, collectionLength);
              });
            }
          }
          lastBlockMap = nextBlockMap;
        });
      };
    }
  };
}];

var NG_HIDE_CLASS = 'ng-hide';
var NG_HIDE_IN_PROGRESS_CLASS = 'ng-hide-animate';
/**
 * @ngdoc directive
 * @name ngShow
 * @multiElement
 *
 * @description
 * The `ngShow` directive shows or hides the given HTML element based on the expression provided to
 * the `ngShow` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is visible) -->
 * <div ng-show="myValue"></div>
 *
 * <!-- when $scope.myValue is falsy (element is hidden) -->
 * <div ng-show="myValue" class="ng-hide"></div>
 * ```
 *
 * When the `ngShow` expression evaluates to a falsy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When truthy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override anything in CSS and the animations will work around the
 * display style.
 *
 * ## A note about animations with `ngShow`
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                        |
 * |-----------------------------------------------------|---------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngShow` expression evaluates to a non truthy value and just before the contents are set to hidden. |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngShow` expression evaluates to a truthy value and just before contents are set to visible.        |
 *
 * @element ANY
 * @param {expression} ngShow If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is shown/hidden respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-simple">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element animate-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-show-complex">
    <file name="index.html">
      Show: <input type="checkbox" ng-model="checked" aria-label="Toggle ngShow"><br />
      <div class="check-element funky-show-hide" ng-show="checked">
        I show up when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngShow', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(false);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(true);
      });
    </file>
  </example>
 */
var ngShowDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngShow, function ngShowWatchAction(value) {
        // we're adding a temporary, animation-specific class for ng-hide since this way
        // we can control when the element is actually displayed on screen without having
        // to have a global/greedy CSS selector that breaks when other animations are run.
        // Read: https://github.com/angular/angular.js/issues/9103#issuecomment-58335845
        $animate[value ? 'removeClass' : 'addClass'](element, NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];


/**
 * @ngdoc directive
 * @name ngHide
 * @multiElement
 *
 * @description
 * The `ngHide` directive shows or hides the given HTML element based on the expression provided to
 * the `ngHide` attribute.
 *
 * The element is shown or hidden by removing or adding the `.ng-hide` CSS class onto the element.
 * The `.ng-hide` CSS class is predefined in AngularJS and sets the display style to none (using an
 * `!important` flag). For CSP mode please add `angular-csp.css` to your HTML file (see
 * {@link ng.directive:ngCsp ngCsp}).
 *
 * ```html
 * <!-- when $scope.myValue is truthy (element is hidden) -->
 * <div ng-hide="myValue" class="ng-hide"></div>
 *
 * <!-- when $scope.myValue is falsy (element is visible) -->
 * <div ng-hide="myValue"></div>
 * ```
 *
 * When the `ngHide` expression evaluates to a truthy value then the `.ng-hide` CSS class is added
 * to the class attribute on the element causing it to become hidden. When falsy, the `.ng-hide`
 * CSS class is removed from the element causing the element not to appear hidden.
 *
 * ## Why is `!important` used?
 *
 * You may be wondering why `!important` is used for the `.ng-hide` CSS class. This is because the
 * `.ng-hide` selector can be easily overridden by heavier selectors. For example, something as
 * simple as changing the display style on a HTML list item would make hidden elements appear
 * visible. This also becomes a bigger issue when dealing with CSS frameworks.
 *
 * By using `!important`, the show and hide behavior will work as expected despite any clash between
 * CSS selector specificity (when `!important` isn't used with any conflicting styles). If a
 * developer chooses to override the styling to change how to hide an element then it is just a
 * matter of using `!important` in their own CSS code.
 *
 * ### Overriding `.ng-hide`
 *
 * By default, the `.ng-hide` class will style the element with `display: none !important`. If you
 * wish to change the hide behavior with `ngShow`/`ngHide`, you can simply overwrite the styles for
 * the `.ng-hide` CSS class. Note that the selector that needs to be used is actually
 * `.ng-hide:not(.ng-hide-animate)` to cope with extra animation classes that can be added.
 *
 * ```css
 * .ng-hide:not(.ng-hide-animate) {
 *   /&#42; These are just alternative ways of hiding an element &#42;/
 *   display: block!important;
 *   position: absolute;
 *   top: -9999px;
 *   left: -9999px;
 * }
 * ```
 *
 * By default you don't need to override in CSS anything and the animations will work around the
 * display style.
 *
 * ## A note about animations with `ngHide`
 *
 * Animations in `ngShow`/`ngHide` work with the show and hide events that are triggered when the
 * directive expression is true and false. This system works like the animation system present with
 * `ngClass` except that you must also include the `!important` flag to override the display
 * property so that the elements are not actually hidden during the animation.
 *
 * ```css
 * /&#42; A working example can be found at the bottom of this page. &#42;/
 * .my-element.ng-hide-add, .my-element.ng-hide-remove {
 *   transition: all 0.5s linear;
 * }
 *
 * .my-element.ng-hide-add { ... }
 * .my-element.ng-hide-add.ng-hide-add-active { ... }
 * .my-element.ng-hide-remove { ... }
 * .my-element.ng-hide-remove.ng-hide-remove-active { ... }
 * ```
 *
 * Keep in mind that, as of AngularJS version 1.3, there is no need to change the display property
 * to block during animation states - ngAnimate will automatically handle the style toggling for you.
 *
 * @animations
 * | Animation                                           | Occurs                                                                                                     |
 * |-----------------------------------------------------|------------------------------------------------------------------------------------------------------------|
 * | {@link $animate#addClass addClass} `.ng-hide`       | After the `ngHide` expression evaluates to a truthy value and just before the contents are set to hidden.  |
 * | {@link $animate#removeClass removeClass} `.ng-hide` | After the `ngHide` expression evaluates to a non truthy value and just before contents are set to visible. |
 *
 *
 * @element ANY
 * @param {expression} ngHide If the {@link guide/expression expression} is truthy/falsy then the
 *                            element is hidden/shown respectively.
 *
 * @example
 * A simple example, animating the element's opacity:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-simple">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element animate-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      .animate-show-hide.ng-hide {
        opacity: 0;
      }

      .animate-show-hide.ng-hide-add,
      .animate-show-hide.ng-hide-remove {
        transition: all linear 0.5s;
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 *
 * <hr />
 * @example
 * A more complex example, featuring different show/hide animations:
 *
  <example module="ngAnimate" deps="angular-animate.js" animations="true" name="ng-hide-complex">
    <file name="index.html">
      Hide: <input type="checkbox" ng-model="checked" aria-label="Toggle ngHide"><br />
      <div class="check-element funky-show-hide" ng-hide="checked">
        I hide when your checkbox is checked.
      </div>
    </file>
    <file name="animations.css">
      body {
        overflow: hidden;
        perspective: 1000px;
      }

      .funky-show-hide.ng-hide-add {
        transform: rotateZ(0);
        transform-origin: right;
        transition: all 0.5s ease-in-out;
      }

      .funky-show-hide.ng-hide-add.ng-hide-add-active {
        transform: rotateZ(-135deg);
      }

      .funky-show-hide.ng-hide-remove {
        transform: rotateY(90deg);
        transform-origin: left;
        transition: all 0.5s ease;
      }

      .funky-show-hide.ng-hide-remove.ng-hide-remove-active {
        transform: rotateY(0);
      }

      .check-element {
        border: 1px solid black;
        opacity: 1;
        padding: 10px;
      }
    </file>
    <file name="protractor.js" type="protractor">
      it('should check ngHide', function() {
        var checkbox = element(by.model('checked'));
        var checkElem = element(by.css('.check-element'));

        expect(checkElem.isDisplayed()).toBe(true);
        checkbox.click();
        expect(checkElem.isDisplayed()).toBe(false);
      });
    </file>
  </example>
 */
var ngHideDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    multiElement: true,
    link: function(scope, element, attr) {
      scope.$watch(attr.ngHide, function ngHideWatchAction(value) {
        // The comment inside of the ngShowDirective explains why we add and
        // remove a temporary class for the show/hide animation
        $animate[value ? 'addClass' : 'removeClass'](element,NG_HIDE_CLASS, {
          tempClasses: NG_HIDE_IN_PROGRESS_CLASS
        });
      });
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngStyle
 * @restrict AC
 *
 * @description
 * The `ngStyle` directive allows you to set CSS style on an HTML element conditionally.
 *
 * @knownIssue
 * You should not use {@link guide/interpolation interpolation} in the value of the `style`
 * attribute, when using the `ngStyle` directive on the same element.
 * See {@link guide/interpolation#known-issues here} for more info.
 *
 * @element ANY
 * @param {expression} ngStyle
 *
 * {@link guide/expression Expression} which evals to an
 * object whose keys are CSS style names and values are corresponding values for those CSS
 * keys.
 *
 * Since some CSS style names are not valid keys for an object, they must be quoted.
 * See the 'background-color' style in the example below.
 *
 * @example
   <example name="ng-style">
     <file name="index.html">
        <input type="button" value="set color" ng-click="myStyle={color:'red'}">
        <input type="button" value="set background" ng-click="myStyle={'background-color':'blue'}">
        <input type="button" value="clear" ng-click="myStyle={}">
        <br/>
        <span ng-style="myStyle">Sample Text</span>
        <pre>myStyle={{myStyle}}</pre>
     </file>
     <file name="style.css">
       span {
         color: black;
       }
     </file>
     <file name="protractor.js" type="protractor">
       var colorSpan = element(by.css('span'));

       it('should check ng-style', function() {
         expect(colorSpan.getCssValue('color')).toBe('rgba(0, 0, 0, 1)');
         element(by.css('input[value=\'set color\']')).click();
         expect(colorSpan.getCssValue('color')).toBe('rgba(255, 0, 0, 1)');
         element(by.css('input[value=clear]')).click();
         expect(colorSpan.getCssValue('color')).toBe('rgba(0, 0, 0, 1)');
       });
     </file>
   </example>
 */
var ngStyleDirective = ngDirective(function(scope, element, attr) {
  scope.$watch(attr.ngStyle, function ngStyleWatchAction(newStyles, oldStyles) {
    if (oldStyles && (newStyles !== oldStyles)) {
      forEach(oldStyles, function(val, style) { element.css(style, '');});
    }
    if (newStyles) element.css(newStyles);
  }, true);
});

/**
 * @ngdoc directive
 * @name ngSwitch
 * @restrict EA
 *
 * @description
 * The `ngSwitch` directive is used to conditionally swap DOM structure on your template based on a scope expression.
 * Elements within `ngSwitch` but without `ngSwitchWhen` or `ngSwitchDefault` directives will be preserved at the location
 * as specified in the template.
 *
 * The directive itself works similar to ngInclude, however, instead of downloading template code (or loading it
 * from the template cache), `ngSwitch` simply chooses one of the nested elements and makes it visible based on which element
 * matches the value obtained from the evaluated expression. In other words, you define a container element
 * (where you place the directive), place an expression on the **`on="..."` attribute**
 * (or the **`ng-switch="..."` attribute**), define any inner elements inside of the directive and place
 * a when attribute per element. The when attribute is used to inform ngSwitch which element to display when the on
 * expression is evaluated. If a matching expression is not found via a when attribute then an element with the default
 * attribute is displayed.
 *
 * <div class="alert alert-info">
 * Be aware that the attribute values to match against cannot be expressions. They are interpreted
 * as literal string values to match against.
 * For example, **`ng-switch-when="someVal"`** will match against the string `"someVal"` not against the
 * value of the expression `$scope.someVal`.
 * </div>

 * @animations
 * | Animation                        | Occurs                              |
 * |----------------------------------|-------------------------------------|
 * | {@link ng.$animate#enter enter}  | after the ngSwitch contents change and the matched child element is placed inside the container |
 * | {@link ng.$animate#leave leave}  | after the ngSwitch contents change and just before the former contents are removed from the DOM |
 *
 * @usage
 *
 * ```
 * <ANY ng-switch="expression">
 *   <ANY ng-switch-when="matchValue1">...</ANY>
 *   <ANY ng-switch-when="matchValue2">...</ANY>
 *   <ANY ng-switch-default>...</ANY>
 * </ANY>
 * ```
 *
 *
 * @scope
 * @priority 1200
 * @param {*} ngSwitch|on expression to match against <code>ng-switch-when</code>.
 * On child elements add:
 *
 * * `ngSwitchWhen`: the case statement to match against. If match then this
 *   case will be displayed. If the same match appears multiple times, all the
 *   elements will be displayed. It is possible to associate multiple values to
 *   the same `ngSwitchWhen` by defining the optional attribute
 *   `ngSwitchWhenSeparator`. The separator will be used to split the value of
 *   the `ngSwitchWhen` attribute into multiple tokens, and the element will show
 *   if any of the `ngSwitch` evaluates to any of these tokens.
 * * `ngSwitchDefault`: the default case when no other case match. If there
 *   are multiple default cases, all of them will be displayed when no other
 *   case match.
 *
 *
 * @example
  <example module="switchExample" deps="angular-animate.js" animations="true" name="ng-switch">
    <file name="index.html">
      <div ng-controller="ExampleController">
        <select ng-model="selection" ng-options="item for item in items">
        </select>
        <code>selection={{selection}}</code>
        <hr/>
        <div class="animate-switch-container"
          ng-switch on="selection">
            <div class="animate-switch" ng-switch-when="settings|options" ng-switch-when-separator="|">Settings Div</div>
            <div class="animate-switch" ng-switch-when="home">Home Span</div>
            <div class="animate-switch" ng-switch-default>default</div>
        </div>
      </div>
    </file>
    <file name="script.js">
      angular.module('switchExample', ['ngAnimate'])
        .controller('ExampleController', ['$scope', function($scope) {
          $scope.items = ['settings', 'home', 'options', 'other'];
          $scope.selection = $scope.items[0];
        }]);
    </file>
    <file name="animations.css">
      .animate-switch-container {
        position:relative;
        background:white;
        border:1px solid black;
        height:40px;
        overflow:hidden;
      }

      .animate-switch {
        padding:10px;
      }

      .animate-switch.ng-animate {
        transition:all cubic-bezier(0.250, 0.460, 0.450, 0.940) 0.5s;

        position:absolute;
        top:0;
        left:0;
        right:0;
        bottom:0;
      }

      .animate-switch.ng-leave.ng-leave-active,
      .animate-switch.ng-enter {
        top:-50px;
      }
      .animate-switch.ng-leave,
      .animate-switch.ng-enter.ng-enter-active {
        top:0;
      }
    </file>
    <file name="protractor.js" type="protractor">
      var switchElem = element(by.css('[ng-switch]'));
      var select = element(by.model('selection'));

      it('should start in settings', function() {
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should change to home', function() {
        select.all(by.css('option')).get(1).click();
        expect(switchElem.getText()).toMatch(/Home Span/);
      });
      it('should change to settings via "options"', function() {
        select.all(by.css('option')).get(2).click();
        expect(switchElem.getText()).toMatch(/Settings Div/);
      });
      it('should select default', function() {
        select.all(by.css('option')).get(3).click();
        expect(switchElem.getText()).toMatch(/default/);
      });
    </file>
  </example>
 */
var ngSwitchDirective = ['$animate', '$compile', function($animate, $compile) {
  return {
    require: 'ngSwitch',

    // asks for $scope to fool the BC controller module
    controller: ['$scope', function NgSwitchController() {
     this.cases = {};
    }],
    link: function(scope, element, attr, ngSwitchController) {
      var watchExpr = attr.ngSwitch || attr.on,
          selectedTranscludes = [],
          selectedElements = [],
          previousLeaveAnimations = [],
          selectedScopes = [];

      var spliceFactory = function(array, index) {
          return function(response) {
            if (response !== false) array.splice(index, 1);
          };
      };

      scope.$watch(watchExpr, function ngSwitchWatchAction(value) {
        var i, ii;

        // Start with the last, in case the array is modified during the loop
        while (previousLeaveAnimations.length) {
          $animate.cancel(previousLeaveAnimations.pop());
        }

        for (i = 0, ii = selectedScopes.length; i < ii; ++i) {
          var selected = getBlockNodes(selectedElements[i].clone);
          selectedScopes[i].$destroy();
          var runner = previousLeaveAnimations[i] = $animate.leave(selected);
          runner.done(spliceFactory(previousLeaveAnimations, i));
        }

        selectedElements.length = 0;
        selectedScopes.length = 0;

        if ((selectedTranscludes = ngSwitchController.cases['!' + value] || ngSwitchController.cases['?'])) {
          forEach(selectedTranscludes, function(selectedTransclude) {
            selectedTransclude.transclude(function(caseElement, selectedScope) {
              selectedScopes.push(selectedScope);
              var anchor = selectedTransclude.element;
              caseElement[caseElement.length++] = $compile.$$createComment('end ngSwitchWhen');
              var block = { clone: caseElement };

              selectedElements.push(block);
              $animate.enter(caseElement, anchor.parent(), anchor);
            });
          });
        }
      });
    }
  };
}];

var ngSwitchWhenDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attrs, ctrl, $transclude) {

    var cases = attrs.ngSwitchWhen.split(attrs.ngSwitchWhenSeparator).sort().filter(
      // Filter duplicate cases
      function(element, index, array) { return array[index - 1] !== element; }
    );

    forEach(cases, function(whenCase) {
      ctrl.cases['!' + whenCase] = (ctrl.cases['!' + whenCase] || []);
      ctrl.cases['!' + whenCase].push({ transclude: $transclude, element: element });
    });
  }
});

var ngSwitchDefaultDirective = ngDirective({
  transclude: 'element',
  priority: 1200,
  require: '^ngSwitch',
  multiElement: true,
  link: function(scope, element, attr, ctrl, $transclude) {
    ctrl.cases['?'] = (ctrl.cases['?'] || []);
    ctrl.cases['?'].push({ transclude: $transclude, element: element });
   }
});

/**
 * @ngdoc directive
 * @name ngTransclude
 * @restrict EAC
 *
 * @description
 * Directive that marks the insertion point for the transcluded DOM of the nearest parent directive that uses transclusion.
 *
 * You can specify that you want to insert a named transclusion slot, instead of the default slot, by providing the slot name
 * as the value of the `ng-transclude` or `ng-transclude-slot` attribute.
 *
 * If the transcluded content is not empty (i.e. contains one or more DOM nodes, including whitespace text nodes), any existing
 * content of this element will be removed before the transcluded content is inserted.
 * If the transcluded content is empty (or only whitespace), the existing content is left intact. This lets you provide fallback
 * content in the case that no transcluded content is provided.
 *
 * @element ANY
 *
 * @param {string} ngTransclude|ngTranscludeSlot the name of the slot to insert at this point. If this is not provided, is empty
 *                                               or its value is the same as the name of the attribute then the default slot is used.
 *
 * @example
 * ### Basic transclusion
 * This example demonstrates basic transclusion of content into a component directive.
 * <example name="simpleTranscludeExample" module="transcludeExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('transcludeExample', [])
 *        .directive('pane', function(){
 *           return {
 *             restrict: 'E',
 *             transclude: true,
 *             scope: { title:'@' },
 *             template: '<div style="border: 1px solid black;">' +
 *                         '<div style="background-color: gray">{{title}}</div>' +
 *                         '<ng-transclude></ng-transclude>' +
 *                       '</div>'
 *           };
 *       })
 *       .controller('ExampleController', ['$scope', function($scope) {
 *         $scope.title = 'Lorem Ipsum';
 *         $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *       }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <input ng-model="title" aria-label="title"> <br/>
 *       <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *       <pane title="{{title}}"><span>{{text}}</span></pane>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.binding('title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *      });
 *   </file>
 * </example>
 *
 * @example
 * ### Transclude fallback content
 * This example shows how to use `NgTransclude` with fallback content, that
 * is displayed if no transcluded content is provided.
 *
 * <example module="transcludeFallbackContentExample" name="ng-transclude">
 * <file name="index.html">
 * <script>
 * angular.module('transcludeFallbackContentExample', [])
 * .directive('myButton', function(){
 *             return {
 *               restrict: 'E',
 *               transclude: true,
 *               scope: true,
 *               template: '<button style="cursor: pointer;">' +
 *                           '<ng-transclude>' +
 *                             '<b style="color: red;">Button1</b>' +
 *                           '</ng-transclude>' +
 *                         '</button>'
 *             };
 *         });
 * </script>
 * <!-- fallback button content -->
 * <my-button id="fallback"></my-button>
 * <!-- modified button content -->
 * <my-button id="modified">
 *   <i style="color: green;">Button2</i>
 * </my-button>
 * </file>
 * <file name="protractor.js" type="protractor">
 * it('should have different transclude element content', function() {
 *          expect(element(by.id('fallback')).getText()).toBe('Button1');
 *          expect(element(by.id('modified')).getText()).toBe('Button2');
 *        });
 * </file>
 * </example>
 *
 * @example
 * ### Multi-slot transclusion
 * This example demonstrates using multi-slot transclusion in a component directive.
 * <example name="multiSlotTranscludeExample" module="multiSlotTranscludeExample">
 *   <file name="index.html">
 *    <style>
 *      .title, .footer {
 *        background-color: gray
 *      }
 *    </style>
 *    <div ng-controller="ExampleController">
 *      <input ng-model="title" aria-label="title"> <br/>
 *      <textarea ng-model="text" aria-label="text"></textarea> <br/>
 *      <pane>
 *        <pane-title><a ng-href="{{link}}">{{title}}</a></pane-title>
 *        <pane-body><p>{{text}}</p></pane-body>
 *      </pane>
 *    </div>
 *   </file>
 *   <file name="app.js">
 *    angular.module('multiSlotTranscludeExample', [])
 *     .directive('pane', function() {
 *        return {
 *          restrict: 'E',
 *          transclude: {
 *            'title': '?paneTitle',
 *            'body': 'paneBody',
 *            'footer': '?paneFooter'
 *          },
 *          template: '<div style="border: 1px solid black;">' +
 *                      '<div class="title" ng-transclude="title">Fallback Title</div>' +
 *                      '<div ng-transclude="body"></div>' +
 *                      '<div class="footer" ng-transclude="footer">Fallback Footer</div>' +
 *                    '</div>'
 *        };
 *    })
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.title = 'Lorem Ipsum';
 *      $scope.link = 'https://google.com';
 *      $scope.text = 'Neque porro quisquam est qui dolorem ipsum quia dolor...';
 *    }]);
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *      it('should have transcluded the title and the body', function() {
 *        var titleElement = element(by.model('title'));
 *        titleElement.clear();
 *        titleElement.sendKeys('TITLE');
 *        var textElement = element(by.model('text'));
 *        textElement.clear();
 *        textElement.sendKeys('TEXT');
 *        expect(element(by.css('.title')).getText()).toEqual('TITLE');
 *        expect(element(by.binding('text')).getText()).toEqual('TEXT');
 *        expect(element(by.css('.footer')).getText()).toEqual('Fallback Footer');
 *      });
 *   </file>
 * </example>
 */
var ngTranscludeMinErr = minErr('ngTransclude');
var ngTranscludeDirective = ['$compile', function($compile) {
  return {
    restrict: 'EAC',
    terminal: true,
    compile: function ngTranscludeCompile(tElement) {

      // Remove and cache any original content to act as a fallback
      var fallbackLinkFn = $compile(tElement.contents());
      tElement.empty();

      return function ngTranscludePostLink($scope, $element, $attrs, controller, $transclude) {

        if (!$transclude) {
          throw ngTranscludeMinErr('orphan',
          'Illegal use of ngTransclude directive in the template! ' +
          'No parent directive that requires a transclusion found. ' +
          'Element: {0}',
          startingTag($element));
        }


        // If the attribute is of the form: `ng-transclude="ng-transclude"` then treat it like the default
        if ($attrs.ngTransclude === $attrs.$attr.ngTransclude) {
          $attrs.ngTransclude = '';
        }
        var slotName = $attrs.ngTransclude || $attrs.ngTranscludeSlot;

        // If the slot is required and no transclusion content is provided then this call will throw an error
        $transclude(ngTranscludeCloneAttachFn, null, slotName);

        // If the slot is optional and no transclusion content is provided then use the fallback content
        if (slotName && !$transclude.isSlotFilled(slotName)) {
          useFallbackContent();
        }

        function ngTranscludeCloneAttachFn(clone, transcludedScope) {
          if (clone.length && notWhitespace(clone)) {
            $element.append(clone);
          } else {
            useFallbackContent();
            // There is nothing linked against the transcluded scope since no content was available,
            // so it should be safe to clean up the generated scope.
            transcludedScope.$destroy();
          }
        }

        function useFallbackContent() {
          // Since this is the fallback content rather than the transcluded content,
          // we link against the scope of this directive rather than the transcluded scope
          fallbackLinkFn($scope, function(clone) {
            $element.append(clone);
          });
        }

        function notWhitespace(nodes) {
          for (var i = 0, ii = nodes.length; i < ii; i++) {
            var node = nodes[i];
            if (node.nodeType !== NODE_TYPE_TEXT || node.nodeValue.trim()) {
              return true;
            }
          }
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name script
 * @restrict E
 *
 * @description
 * Load the content of a `<script>` element into {@link ng.$templateCache `$templateCache`}, so that the
 * template can be used by {@link ng.directive:ngInclude `ngInclude`},
 * {@link ngRoute.directive:ngView `ngView`}, or {@link guide/directive directives}. The type of the
 * `<script>` element must be specified as `text/ng-template`, and a cache name for the template must be
 * assigned through the element's `id`, which can then be used as a directive's `templateUrl`.
 *
 * @param {string} type Must be set to `'text/ng-template'`.
 * @param {string} id Cache name of the template.
 *
 * @example
  <example  name="script-tag">
    <file name="index.html">
      <script type="text/ng-template" id="/tpl.html">
        Content of the template.
      </script>

      <a ng-click="currentTpl='/tpl.html'" id="tpl-link">Load inlined template</a>
      <div id="tpl-content" ng-include src="currentTpl"></div>
    </file>
    <file name="protractor.js" type="protractor">
      it('should load template defined inside script tag', function() {
        element(by.css('#tpl-link')).click();
        expect(element(by.css('#tpl-content')).getText()).toMatch(/Content of the template/);
      });
    </file>
  </example>
 */
var scriptDirective = ['$templateCache', function($templateCache) {
  return {
    restrict: 'E',
    terminal: true,
    compile: function(element, attr) {
      if (attr.type === 'text/ng-template') {
        var templateUrl = attr.id,
            text = element[0].text;

        $templateCache.put(templateUrl, text);
      }
    }
  };
}];

/* exported selectDirective, optionDirective */

var noopNgModelController = { $setViewValue: noop, $render: noop };

function setOptionSelectedStatus(optionEl, value) {
  optionEl.prop('selected', value);
  /**
   * When unselecting an option, setting the property to null / false should be enough
   * However, screenreaders might react to the selected attribute instead, see
   * https://github.com/angular/angular.js/issues/14419
   * Note: "selected" is a boolean attr and will be removed when the "value" arg in attr() is false
   * or null
   */
  optionEl.attr('selected', value);
}

/**
 * @ngdoc type
 * @name  select.SelectController
 *
 * @description
 * The controller for the {@link ng.select select} directive. The controller exposes
 * a few utility methods that can be used to augment the behavior of a regular or an
 * {@link ng.ngOptions ngOptions} select element.
 *
 * @example
 * ### Set a custom error when the unknown option is selected
 *
 * This example sets a custom error "unknownValue" on the ngModelController
 * when the select element's unknown option is selected, i.e. when the model is set to a value
 * that is not matched by any option.
 *
 * <example name="select-unknown-value-error" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="testSelect"> Single select: </label><br>
 *     <select name="testSelect" ng-model="selected" unknown-value-error>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <span ng-if="myForm.testSelect.$error.unknownValue">Error: The current model doesn't match any option</span>
 *
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.selected = null;
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.selected = 'nonsense';
 *      };
 *   }])
 *   .directive('unknownValueError', function() {
 *     return {
 *       require: ['ngModel', 'select'],
 *       link: function(scope, element, attrs, ctrls) {
 *         var ngModelCtrl = ctrls[0];
 *         var selectCtrl = ctrls[1];
 *
 *         ngModelCtrl.$validators.unknownValue = function(modelValue, viewValue) {
 *           if (selectCtrl.$isUnknownOptionSelected()) {
 *             return false;
 *           }
 *
 *           return true;
 *         };
 *       }
 *
 *     };
 *   });
 * </file>
 *</example>
 *
 *
 * @example
 * ### Set the "required" error when the unknown option is selected.
 *
 * By default, the "required" error on the ngModelController is only set on a required select
 * when the empty option is selected. This example adds a custom directive that also sets the
 * error when the unknown option is selected.
 *
 * <example name="select-unknown-value-required" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="testSelect"> Select: </label><br>
 *     <select name="testSelect" ng-model="selected" unknown-value-required>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <span ng-if="myForm.testSelect.$error.required">Error: Please select a value</span><br>
 *
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.selected = null;
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.selected = 'nonsense';
 *      };
 *   }])
 *   .directive('unknownValueRequired', function() {
 *     return {
 *       priority: 1, // This directive must run after the required directive has added its validator
 *       require: ['ngModel', 'select'],
 *       link: function(scope, element, attrs, ctrls) {
 *         var ngModelCtrl = ctrls[0];
 *         var selectCtrl = ctrls[1];
 *
 *         var originalRequiredValidator = ngModelCtrl.$validators.required;
 *
 *         ngModelCtrl.$validators.required = function() {
 *           if (attrs.required && selectCtrl.$isUnknownOptionSelected()) {
 *             return false;
 *           }
 *
 *           return originalRequiredValidator.apply(this, arguments);
 *         };
 *       }
 *     };
 *   });
 * </file>
 *</example>
 *
 *
 */
var SelectController =
        ['$element', '$scope', /** @this */ function($element, $scope) {

  var self = this,
      optionsMap = new NgMap();

  self.selectValueMap = {}; // Keys are the hashed values, values the original values

  // If the ngModel doesn't get provided then provide a dummy noop version to prevent errors
  self.ngModelCtrl = noopNgModelController;
  self.multiple = false;

  // The "unknown" option is one that is prepended to the list if the viewValue
  // does not match any of the options. When it is rendered the value of the unknown
  // option is '? XXX ?' where XXX is the hashKey of the value that is not known.
  //
  // Support: IE 9 only
  // We can't just jqLite('<option>') since jqLite is not smart enough
  // to create it in <select> and IE barfs otherwise.
  self.unknownOption = jqLite(window.document.createElement('option'));

  // The empty option is an option with the value '' that the application developer can
  // provide inside the select. It is always selectable and indicates that a "null" selection has
  // been made by the user.
  // If the select has an empty option, and the model of the select is set to "undefined" or "null",
  // the empty option is selected.
  // If the model is set to a different unmatched value, the unknown option is rendered and
  // selected, i.e both are present, because a "null" selection and an unknown value are different.
  self.hasEmptyOption = false;
  self.emptyOption = undefined;

  self.renderUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    $element.prepend(self.unknownOption);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.updateUnknownOption = function(val) {
    var unknownVal = self.generateUnknownOptionValue(val);
    self.unknownOption.val(unknownVal);
    setOptionSelectedStatus(self.unknownOption, true);
    $element.val(unknownVal);
  };

  self.generateUnknownOptionValue = function(val) {
    return '? ' + hashKey(val) + ' ?';
  };

  self.removeUnknownOption = function() {
    if (self.unknownOption.parent()) self.unknownOption.remove();
  };

  self.selectEmptyOption = function() {
    if (self.emptyOption) {
      $element.val('');
      setOptionSelectedStatus(self.emptyOption, true);
    }
  };

  self.unselectEmptyOption = function() {
    if (self.hasEmptyOption) {
      setOptionSelectedStatus(self.emptyOption, false);
    }
  };

  $scope.$on('$destroy', function() {
    // disable unknown option so that we don't do work when the whole select is being destroyed
    self.renderUnknownOption = noop;
  });

  // Read the value of the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.readValue = function readSingleValue() {
    var val = $element.val();
    // ngValue added option values are stored in the selectValueMap, normal interpolations are not
    var realVal = val in self.selectValueMap ? self.selectValueMap[val] : val;

    if (self.hasOption(realVal)) {
      return realVal;
    }

    return null;
  };


  // Write the value to the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  self.writeValue = function writeSingleValue(value) {
    // Make sure to remove the selected attribute from the previously selected option
    // Otherwise, screen readers might get confused
    var currentlySelectedOption = $element[0].options[$element[0].selectedIndex];
    if (currentlySelectedOption) setOptionSelectedStatus(jqLite(currentlySelectedOption), false);

    if (self.hasOption(value)) {
      self.removeUnknownOption();

      var hashedVal = hashKey(value);
      $element.val(hashedVal in self.selectValueMap ? hashedVal : value);

      // Set selected attribute and property on selected option for screen readers
      var selectedOption = $element[0].options[$element[0].selectedIndex];
      setOptionSelectedStatus(jqLite(selectedOption), true);
    } else {
      self.selectUnknownOrEmptyOption(value);
    }
  };


  // Tell the select control that an option, with the given value, has been added
  self.addOption = function(value, element) {
    // Skip comment nodes, as they only pollute the `optionsMap`
    if (element[0].nodeType === NODE_TYPE_COMMENT) return;

    assertNotHasOwnProperty(value, '"option value"');
    if (value === '') {
      self.hasEmptyOption = true;
      self.emptyOption = element;
    }
    var count = optionsMap.get(value) || 0;
    optionsMap.set(value, count + 1);
    // Only render at the end of a digest. This improves render performance when many options
    // are added during a digest and ensures all relevant options are correctly marked as selected
    scheduleRender();
  };

  // Tell the select control that an option, with the given value, has been removed
  self.removeOption = function(value) {
    var count = optionsMap.get(value);
    if (count) {
      if (count === 1) {
        optionsMap.delete(value);
        if (value === '') {
          self.hasEmptyOption = false;
          self.emptyOption = undefined;
        }
      } else {
        optionsMap.set(value, count - 1);
      }
    }
  };

  // Check whether the select control has an option matching the given value
  self.hasOption = function(value) {
    return !!optionsMap.get(value);
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$hasEmptyOption
   *
   * @description
   *
   * Returns `true` if the select element currently has an empty option
   * element, i.e. an option that signifies that the select is empty / the selection is null.
   *
   */
  self.$hasEmptyOption = function() {
    return self.hasEmptyOption;
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$isUnknownOptionSelected
   *
   * @description
   *
   * Returns `true` if the select element's unknown option is selected. The unknown option is added
   * and automatically selected whenever the select model doesn't match any option.
   *
   */
  self.$isUnknownOptionSelected = function() {
    // Presence of the unknown option means it is selected
    return $element[0].options[0] === self.unknownOption[0];
  };

  /**
   * @ngdoc method
   * @name select.SelectController#$isEmptyOptionSelected
   *
   * @description
   *
   * Returns `true` if the select element has an empty option and this empty option is currently
   * selected. Returns `false` if the select element has no empty option or it is not selected.
   *
   */
  self.$isEmptyOptionSelected = function() {
    return self.hasEmptyOption && $element[0].options[$element[0].selectedIndex] === self.emptyOption[0];
  };

  self.selectUnknownOrEmptyOption = function(value) {
    if (value == null && self.emptyOption) {
      self.removeUnknownOption();
      self.selectEmptyOption();
    } else if (self.unknownOption.parent().length) {
      self.updateUnknownOption(value);
    } else {
      self.renderUnknownOption(value);
    }
  };

  var renderScheduled = false;
  function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    $scope.$$postDigest(function() {
      renderScheduled = false;
      self.ngModelCtrl.$render();
    });
  }

  var updateScheduled = false;
  function scheduleViewValueUpdate(renderAfter) {
    if (updateScheduled) return;

    updateScheduled = true;

    $scope.$$postDigest(function() {
      if ($scope.$$destroyed) return;

      updateScheduled = false;
      self.ngModelCtrl.$setViewValue(self.readValue());
      if (renderAfter) self.ngModelCtrl.$render();
    });
  }


  self.registerOption = function(optionScope, optionElement, optionAttrs, interpolateValueFn, interpolateTextFn) {

    if (optionAttrs.$attr.ngValue) {
      // The value attribute is set by ngValue
      var oldVal, hashedVal = NaN;
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(hashedVal)) {
          self.removeOption(oldVal);
          delete self.selectValueMap[hashedVal];
          removal = true;
        }

        hashedVal = hashKey(newVal);
        oldVal = newVal;
        self.selectValueMap[hashedVal] = newVal;
        self.addOption(newVal, optionElement);
        // Set the attribute directly instead of using optionAttrs.$set - this stops the observer
        // from firing a second time. Other $observers on value will also get the result of the
        // ngValue expression, not the hashed value
        optionElement.attr('value', hashedVal);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }

      });
    } else if (interpolateValueFn) {
      // The value attribute is interpolated
      optionAttrs.$observe('value', function valueAttributeObserveAction(newVal) {
        // This method is overwritten in ngOptions and has side-effects!
        self.readValue();

        var removal;
        var previouslySelected = optionElement.prop('selected');

        if (isDefined(oldVal)) {
          self.removeOption(oldVal);
          removal = true;
        }
        oldVal = newVal;
        self.addOption(newVal, optionElement);

        if (removal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else if (interpolateTextFn) {
      // The text content is interpolated
      optionScope.$watch(interpolateTextFn, function interpolateWatchAction(newVal, oldVal) {
        optionAttrs.$set('value', newVal);
        var previouslySelected = optionElement.prop('selected');
        if (oldVal !== newVal) {
          self.removeOption(oldVal);
        }
        self.addOption(newVal, optionElement);

        if (oldVal && previouslySelected) {
          scheduleViewValueUpdate();
        }
      });
    } else {
      // The value attribute is static
      self.addOption(optionAttrs.value, optionElement);
    }


    optionAttrs.$observe('disabled', function(newVal) {

      // Since model updates will also select disabled options (like ngOptions),
      // we only have to handle options becoming disabled, not enabled

      if (newVal === 'true' || newVal && optionElement.prop('selected')) {
        if (self.multiple) {
          scheduleViewValueUpdate(true);
        } else {
          self.ngModelCtrl.$setViewValue(null);
          self.ngModelCtrl.$render();
        }
      }
    });

    optionElement.on('$destroy', function() {
      var currentValue = self.readValue();
      var removeValue = optionAttrs.value;

      self.removeOption(removeValue);
      scheduleRender();

      if (self.multiple && currentValue && currentValue.indexOf(removeValue) !== -1 ||
          currentValue === removeValue
      ) {
        // When multiple (selected) options are destroyed at the same time, we don't want
        // to run a model update for each of them. Instead, run a single update in the $$postDigest
        scheduleViewValueUpdate(true);
      }
    });
  };
}];

/**
 * @ngdoc directive
 * @name select
 * @restrict E
 *
 * @description
 * HTML `select` element with angular data-binding.
 *
 * The `select` directive is used together with {@link ngModel `ngModel`} to provide data-binding
 * between the scope and the `<select>` control (including setting default values).
 * It also handles dynamic `<option>` elements, which can be added using the {@link ngRepeat `ngRepeat}` or
 * {@link ngOptions `ngOptions`} directives.
 *
 * When an item in the `<select>` menu is selected, the value of the selected option will be bound
 * to the model identified by the `ngModel` directive. With static or repeated options, this is
 * the content of the `value` attribute or the textContent of the `<option>`, if the value attribute is missing.
 * Value and textContent can be interpolated.
 *
 * The {@link select.SelectController select controller} exposes utility functions that can be used
 * to manipulate the select's behavior.
 *
 * ## Matching model and option values
 *
 * In general, the match between the model and an option is evaluated by strictly comparing the model
 * value against the value of the available options.
 *
 * If you are setting the option value with the option's `value` attribute, or textContent, the
 * value will always be a `string` which means that the model value must also be a string.
 * Otherwise the `select` directive cannot match them correctly.
 *
 * To bind the model to a non-string value, you can use one of the following strategies:
 * - the {@link ng.ngOptions `ngOptions`} directive
 *   ({@link ng.select#using-select-with-ngoptions-and-setting-a-default-value})
 * - the {@link ng.ngValue `ngValue`} directive, which allows arbitrary expressions to be
 *   option values ({@link ng.select#using-ngvalue-to-bind-the-model-to-an-array-of-objects Example})
 * - model $parsers / $formatters to convert the string value
 *   ({@link ng.select#binding-select-to-a-non-string-value-via-ngmodel-parsing-formatting Example})
 *
 * If the viewValue of `ngModel` does not match any of the options, then the control
 * will automatically add an "unknown" option, which it then removes when the mismatch is resolved.
 *
 * Optionally, a single hard-coded `<option>` element, with the value set to an empty string, can
 * be nested into the `<select>` element. This element will then represent the `null` or "not selected"
 * option. See example below for demonstration.
 *
 * ## Choosing between `ngRepeat` and `ngOptions`
 *
 * In many cases, `ngRepeat` can be used on `<option>` elements instead of {@link ng.directive:ngOptions
 * ngOptions} to achieve a similar result. However, `ngOptions` provides some benefits:
 * - more flexibility in how the `<select>`'s model is assigned via the `select` **`as`** part of the
 * comprehension expression
 * - reduced memory consumption by not creating a new scope for each repeated instance
 * - increased render speed by creating the options in a documentFragment instead of individually
 *
 * Specifically, select with repeated options slows down significantly starting at 2000 options in
 * Chrome and Internet Explorer / Edge.
 *
 *
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} multiple Allows multiple options to be selected. The selected values will be
 *     bound to the model as an array.
 * @param {string=} required Sets `required` validation error key if the value is not entered.
 * @param {string=} ngRequired Adds required attribute and required validation constraint to
 * the element when the ngRequired expression evaluates to true. Use ngRequired instead of required
 * when you want to data-bind to the required attribute.
 * @param {string=} ngChange Angular expression to be executed when selected option(s) changes due to user
 *    interaction with the select element.
 * @param {string=} ngOptions sets the options that the select is populated with and defines what is
 * set on the model on selection. See {@link ngOptions `ngOptions`}.
 * @param {string=} ngAttrSize sets the size of the select element dynamically. Uses the
 * {@link guide/interpolation#-ngattr-for-binding-to-arbitrary-attributes ngAttr} directive.
 *
 *
 * @knownIssue
 *
 * In Firefox, the select model is only updated when the select element is blurred. For example,
 * when switching between options with the keyboard, the select model is only set to the
 * currently selected option when the select is blurred, e.g via tab key or clicking the mouse
 * outside the select.
 *
 * This is due to an ambiguity in the select element specification. See the
 * [issue on the Firefox bug tracker](https://bugzilla.mozilla.org/show_bug.cgi?id=126379)
 * for more information, and this
 * [Github comment for a workaround](https://github.com/angular/angular.js/issues/9134#issuecomment-130800488)
 *
 * @example
 * ### Simple `select` elements with static options
 *
 * <example name="static-select" module="staticSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="singleSelect"> Single select: </label><br>
 *     <select name="singleSelect" ng-model="data.singleSelect">
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *
 *     <label for="singleSelect"> Single select with "not selected" option and dynamic option values: </label><br>
 *     <select name="singleSelect" id="singleSelect" ng-model="data.singleSelect">
 *       <option value="">---Please select---</option> <!-- not selected / blank option -->
 *       <option value="{{data.option1}}">Option 1</option> <!-- interpolation -->
 *       <option value="option-2">Option 2</option>
 *     </select><br>
 *     <button ng-click="forceUnknownOption()">Force unknown option</button><br>
 *     <tt>singleSelect = {{data.singleSelect}}</tt>
 *
 *     <hr>
 *     <label for="multipleSelect"> Multiple select: </label><br>
 *     <select name="multipleSelect" id="multipleSelect" ng-model="data.multipleSelect" multiple>
 *       <option value="option-1">Option 1</option>
 *       <option value="option-2">Option 2</option>
 *       <option value="option-3">Option 3</option>
 *     </select><br>
 *     <tt>multipleSelect = {{data.multipleSelect}}</tt><br/>
 *   </form>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('staticSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       singleSelect: null,
 *       multipleSelect: [],
 *       option1: 'option-1'
 *      };
 *
 *      $scope.forceUnknownOption = function() {
 *        $scope.data.singleSelect = 'nonsense';
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `ngRepeat` to generate `select` options
 * <example name="select-ngrepeat" module="ngrepeatSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="repeatSelect"> Repeat select: </label>
 *     <select name="repeatSelect" id="repeatSelect" ng-model="data.model">
 *       <option ng-repeat="option in data.availableOptions" value="{{option.id}}">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <tt>model = {{data.model}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngrepeatSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `ngValue` to bind the model to an array of objects
 * <example name="select-ngvalue" module="ngvalueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="ngvalueselect"> ngvalue select: </label>
 *     <select size="6" name="ngvalueselect" ng-model="data.model" multiple>
 *       <option ng-repeat="option in data.availableOptions" ng-value="option.value">{{option.name}}</option>
 *     </select>
 *   </form>
 *   <hr>
 *   <pre>model = {{data.model | json}}</pre><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('ngvalueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       model: null,
 *       availableOptions: [
           {value: 'myString', name: 'string'},
           {value: 1, name: 'integer'},
           {value: true, name: 'boolean'},
           {value: null, name: 'null'},
           {value: {prop: 'value'}, name: 'object'},
           {value: ['a'], name: 'array'}
 *       ]
 *      };
 *   }]);
 * </file>
 *</example>
 *
 * ### Using `select` with `ngOptions` and setting a default value
 * See the {@link ngOptions ngOptions documentation} for more `ngOptions` usage examples.
 *
 * <example name="select-with-default-values" module="defaultValueSelect">
 * <file name="index.html">
 * <div ng-controller="ExampleController">
 *   <form name="myForm">
 *     <label for="mySelect">Make a choice:</label>
 *     <select name="mySelect" id="mySelect"
 *       ng-options="option.name for option in data.availableOptions track by option.id"
 *       ng-model="data.selectedOption"></select>
 *   </form>
 *   <hr>
 *   <tt>option = {{data.selectedOption}}</tt><br/>
 * </div>
 * </file>
 * <file name="app.js">
 *  angular.module('defaultValueSelect', [])
 *    .controller('ExampleController', ['$scope', function($scope) {
 *      $scope.data = {
 *       availableOptions: [
 *         {id: '1', name: 'Option A'},
 *         {id: '2', name: 'Option B'},
 *         {id: '3', name: 'Option C'}
 *       ],
 *       selectedOption: {id: '3', name: 'Option C'} //This sets the default value of the select in the ui
 *       };
 *   }]);
 * </file>
 *</example>
 *
 *
 * ### Binding `select` to a non-string value via `ngModel` parsing / formatting
 *
 * <example name="select-with-non-string-options" module="nonStringSelect">
 *   <file name="index.html">
 *     <select ng-model="model.id" convert-to-number>
 *       <option value="0">Zero</option>
 *       <option value="1">One</option>
 *       <option value="2">Two</option>
 *     </select>
 *     {{ model }}
 *   </file>
 *   <file name="app.js">
 *     angular.module('nonStringSelect', [])
 *       .run(function($rootScope) {
 *         $rootScope.model = { id: 2 };
 *       })
 *       .directive('convertToNumber', function() {
 *         return {
 *           require: 'ngModel',
 *           link: function(scope, element, attrs, ngModel) {
 *             ngModel.$parsers.push(function(val) {
 *               return parseInt(val, 10);
 *             });
 *             ngModel.$formatters.push(function(val) {
 *               return '' + val;
 *             });
 *           }
 *         };
 *       });
 *   </file>
 *   <file name="protractor.js" type="protractor">
 *     it('should initialize to model', function() {
 *       expect(element(by.model('model.id')).$('option:checked').getText()).toEqual('Two');
 *     });
 *   </file>
 * </example>
 *
 */
var selectDirective = function() {

  return {
    restrict: 'E',
    require: ['select', '?ngModel'],
    controller: SelectController,
    priority: 1,
    link: {
      pre: selectPreLink,
      post: selectPostLink
    }
  };

  function selectPreLink(scope, element, attr, ctrls) {

      var selectCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1];

      // if ngModel is not defined, we don't need to do anything but set the registerOption
      // function to noop, so options don't get added internally
      if (!ngModelCtrl) {
        selectCtrl.registerOption = noop;
        return;
      }


      selectCtrl.ngModelCtrl = ngModelCtrl;

      // When the selected item(s) changes we delegate getting the value of the select control
      // to the `readValue` method, which can be changed if the select can have multiple
      // selected values or if the options are being generated by `ngOptions`
      element.on('change', function() {
        selectCtrl.removeUnknownOption();
        scope.$apply(function() {
          ngModelCtrl.$setViewValue(selectCtrl.readValue());
        });
      });

      // If the select allows multiple values then we need to modify how we read and write
      // values from and to the control; also what it means for the value to be empty and
      // we have to add an extra watch since ngModel doesn't work well with arrays - it
      // doesn't trigger rendering if only an item in the array changes.
      if (attr.multiple) {
        selectCtrl.multiple = true;

        // Read value now needs to check each option to see if it is selected
        selectCtrl.readValue = function readMultipleValue() {
          var array = [];
          forEach(element.find('option'), function(option) {
            if (option.selected && !option.disabled) {
              var val = option.value;
              array.push(val in selectCtrl.selectValueMap ? selectCtrl.selectValueMap[val] : val);
            }
          });
          return array;
        };

        // Write value now needs to set the selected property of each matching option
        selectCtrl.writeValue = function writeMultipleValue(value) {
          forEach(element.find('option'), function(option) {
            var shouldBeSelected = !!value && (includes(value, option.value) ||
                                               includes(value, selectCtrl.selectValueMap[option.value]));
            var currentlySelected = option.selected;

            // Support: IE 9-11 only, Edge 12-15+
            // In IE and Edge adding options to the selection via shift+click/UP/DOWN
            // will de-select already selected options if "selected" on those options was set
            // more than once (i.e. when the options were already selected)
            // So we only modify the selected property if necessary.
            // Note: this behavior cannot be replicated via unit tests because it only shows in the
            // actual user interface.
            if (shouldBeSelected !== currentlySelected) {
              setOptionSelectedStatus(jqLite(option), shouldBeSelected);
            }

          });
        };

        // we have to do it on each watch since ngModel watches reference, but
        // we need to work of an array, so we need to see if anything was inserted/removed
        var lastView, lastViewRef = NaN;
        scope.$watch(function selectMultipleWatch() {
          if (lastViewRef === ngModelCtrl.$viewValue && !equals(lastView, ngModelCtrl.$viewValue)) {
            lastView = shallowCopy(ngModelCtrl.$viewValue);
            ngModelCtrl.$render();
          }
          lastViewRef = ngModelCtrl.$viewValue;
        });

        // If we are a multiple select then value is now a collection
        // so the meaning of $isEmpty changes
        ngModelCtrl.$isEmpty = function(value) {
          return !value || value.length === 0;
        };

      }
    }

    function selectPostLink(scope, element, attrs, ctrls) {
      // if ngModel is not defined, we don't need to do anything
      var ngModelCtrl = ctrls[1];
      if (!ngModelCtrl) return;

      var selectCtrl = ctrls[0];

      // We delegate rendering to the `writeValue` method, which can be changed
      // if the select can have multiple selected values or if the options are being
      // generated by `ngOptions`.
      // This must be done in the postLink fn to prevent $render to be called before
      // all nodes have been linked correctly.
      ngModelCtrl.$render = function() {
        selectCtrl.writeValue(ngModelCtrl.$viewValue);
      };
    }
};


// The option directive is purely designed to communicate the existence (or lack of)
// of dynamically created (and destroyed) option elements to their containing select
// directive via its controller.
var optionDirective = ['$interpolate', function($interpolate) {
  return {
    restrict: 'E',
    priority: 100,
    compile: function(element, attr) {
      var interpolateValueFn, interpolateTextFn;

      if (isDefined(attr.ngValue)) {
        // Will be handled by registerOption
      } else if (isDefined(attr.value)) {
        // If the value attribute is defined, check if it contains an interpolation
        interpolateValueFn = $interpolate(attr.value, true);
      } else {
        // If the value attribute is not defined then we fall back to the
        // text content of the option element, which may be interpolated
        interpolateTextFn = $interpolate(element.text(), true);
        if (!interpolateTextFn) {
          attr.$set('value', element.text());
        }
      }

      return function(scope, element, attr) {
        // This is an optimization over using ^^ since we don't want to have to search
        // all the way to the root of the DOM for every single option element
        var selectCtrlName = '$selectController',
            parent = element.parent(),
            selectCtrl = parent.data(selectCtrlName) ||
              parent.parent().data(selectCtrlName); // in case we are in optgroup

        if (selectCtrl) {
          selectCtrl.registerOption(scope, element, attr, interpolateValueFn, interpolateTextFn);
        }
      };
    }
  };
}];

/**
 * @ngdoc directive
 * @name ngRequired
 * @restrict A
 *
 * @description
 *
 * ngRequired adds the required {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for {@link input `input`} and {@link select `select`} controls, but can also be
 * applied to custom controls.
 *
 * The directive sets the `required` attribute on the element if the Angular expression inside
 * `ngRequired` evaluates to true. A special directive for setting `required` is necessary because we
 * cannot use interpolation inside `required`. See the {@link guide/interpolation interpolation guide}
 * for more info.
 *
 * The validator will set the `required` error key to true if the `required` attribute is set and
 * calling {@link ngModel.NgModelController#$isEmpty `NgModelController.$isEmpty`} with the
 * {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`} returns `true`. For example, the
 * `$isEmpty()` implementation for `input[text]` checks the length of the `$viewValue`. When developing
 * custom controls, `$isEmpty()` can be overwritten to account for a $viewValue that is not string-based.
 *
 * @example
 * <example name="ngRequiredDirective" module="ngRequiredExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngRequiredExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.required = true;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="required">Toggle required: </label>
 *         <input type="checkbox" ng-model="required" id="required" />
 *         <br>
 *         <label for="input">This input must be filled if `required` is true: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-required="required" /><br>
 *         <hr>
 *         required error set? = <code>{{form.input.$error.required}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var required = element(by.binding('form.input.$error.required'));
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should set the required error', function() {
         expect(required.getText()).toContain('true');

         input.sendKeys('123');
         expect(required.getText()).not.toContain('true');
         expect(model.getText()).toContain('123');
       });
 *   </file>
 * </example>
 */
var requiredDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;
      attr.required = true; // force truthy in case we are on non input element

      ctrl.$validators.required = function(modelValue, viewValue) {
        return !attr.required || !ctrl.$isEmpty(viewValue);
      };

      attr.$observe('required', function() {
        ctrl.$validate();
      });
    }
  };
};

/**
 * @ngdoc directive
 * @name ngPattern
 *
 * @description
 *
 * ngPattern adds the pattern {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `pattern` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * does not match a RegExp which is obtained by evaluating the Angular expression given in the
 * `ngPattern` attribute value:
 * * If the expression evaluates to a RegExp object, then this is used directly.
 * * If the expression evaluates to a string, then it will be converted to a RegExp after wrapping it
 * in `^` and `$` characters. For instance, `"abc"` will be converted to `new RegExp('^abc$')`.
 *
 * <div class="alert alert-info">
 * **Note:** Avoid using the `g` flag on the RegExp, as it will cause each successive search to
 * start at the index of the last search's match, thus not taking the whole input value into
 * account.
 * </div>
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `pattern` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngPattern` does not set the `pattern` attribute and therefore HTML5 constraint validation is
 *     not available.
 *   </li>
 *   <li>
 *     The `ngPattern` attribute must be an expression, while the `pattern` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngPatternDirective" module="ngPatternExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngPatternExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.regex = '\\d+';
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="regex">Set a pattern (regex string): </label>
 *         <input type="text" ng-model="regex" id="regex" />
 *         <br>
 *         <label for="input">This input is restricted by the current pattern: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-pattern="regex" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default pattern', function() {
         input.sendKeys('aaa');
         expect(model.getText()).not.toContain('aaa');

         input.clear().then(function() {
           input.sendKeys('123');
           expect(model.getText()).toContain('123');
         });
       });
 *   </file>
 * </example>
 */
var patternDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var regexp, patternExp = attr.ngPattern || attr.pattern;
      attr.$observe('pattern', function(regex) {
        if (isString(regex) && regex.length > 0) {
          regex = new RegExp('^' + regex + '$');
        }

        if (regex && !regex.test) {
          throw minErr('ngPattern')('noregexp',
            'Expected {0} to be a RegExp but was {1}. Element: {2}', patternExp,
            regex, startingTag(elm));
        }

        regexp = regex || undefined;
        ctrl.$validate();
      });

      ctrl.$validators.pattern = function(modelValue, viewValue) {
        // HTML5 pattern constraint validates the input value, so we validate the viewValue
        return ctrl.$isEmpty(viewValue) || isUndefined(regexp) || regexp.test(viewValue);
      };
    }
  };
};

/**
 * @ngdoc directive
 * @name ngMaxlength
 *
 * @description
 *
 * ngMaxlength adds the maxlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `maxlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is longer than the integer obtained by evaluating the Angular expression given in the
 * `ngMaxlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `maxlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMaxlength` does not set the `maxlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMaxlength` attribute must be an expression, while the `maxlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMaxlengthDirective" module="ngMaxlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMaxlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.maxlength = 5;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="maxlength">Set a maxlength: </label>
 *         <input type="number" ng-model="maxlength" id="maxlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current maxlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-maxlength="maxlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default maxlength', function() {
         input.sendKeys('abcdef');
         expect(model.getText()).not.toContain('abcdef');

         input.clear().then(function() {
           input.sendKeys('abcde');
           expect(model.getText()).toContain('abcde');
         });
       });
 *   </file>
 * </example>
 */
var maxlengthDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var maxlength = -1;
      attr.$observe('maxlength', function(value) {
        var intVal = toInt(value);
        maxlength = isNumberNaN(intVal) ? -1 : intVal;
        ctrl.$validate();
      });
      ctrl.$validators.maxlength = function(modelValue, viewValue) {
        return (maxlength < 0) || ctrl.$isEmpty(viewValue) || (viewValue.length <= maxlength);
      };
    }
  };
};

/**
 * @ngdoc directive
 * @name ngMinlength
 *
 * @description
 *
 * ngMinlength adds the minlength {@link ngModel.NgModelController#$validators `validator`} to {@link ngModel `ngModel`}.
 * It is most often used for text-based {@link input `input`} controls, but can also be applied to custom text-based controls.
 *
 * The validator sets the `minlength` error key if the {@link ngModel.NgModelController#$viewValue `ngModel.$viewValue`}
 * is shorter than the integer obtained by evaluating the Angular expression given in the
 * `ngMinlength` attribute value.
 *
 * <div class="alert alert-info">
 * **Note:** This directive is also added when the plain `minlength` attribute is used, with two
 * differences:
 * <ol>
 *   <li>
 *     `ngMinlength` does not set the `minlength` attribute and therefore HTML5 constraint
 *     validation is not available.
 *   </li>
 *   <li>
 *     The `ngMinlength` value must be an expression, while the `minlength` value must be
 *     interpolated.
 *   </li>
 * </ol>
 * </div>
 *
 * @example
 * <example name="ngMinlengthDirective" module="ngMinlengthExample">
 *   <file name="index.html">
 *     <script>
 *       angular.module('ngMinlengthExample', [])
 *         .controller('ExampleController', ['$scope', function($scope) {
 *           $scope.minlength = 3;
 *         }]);
 *     </script>
 *     <div ng-controller="ExampleController">
 *       <form name="form">
 *         <label for="minlength">Set a minlength: </label>
 *         <input type="number" ng-model="minlength" id="minlength" />
 *         <br>
 *         <label for="input">This input is restricted by the current minlength: </label>
 *         <input type="text" ng-model="model" id="input" name="input" ng-minlength="minlength" /><br>
 *         <hr>
 *         input valid? = <code>{{form.input.$valid}}</code><br>
 *         model = <code>{{model}}</code>
 *       </form>
 *     </div>
 *   </file>
 *   <file name="protractor.js" type="protractor">
       var model = element(by.binding('model'));
       var input = element(by.id('input'));

       it('should validate the input with the default minlength', function() {
         input.sendKeys('ab');
         expect(model.getText()).not.toContain('ab');

         input.sendKeys('abc');
         expect(model.getText()).toContain('abc');
       });
 *   </file>
 * </example>
 */
var minlengthDirective = function() {
  return {
    restrict: 'A',
    require: '?ngModel',
    link: function(scope, elm, attr, ctrl) {
      if (!ctrl) return;

      var minlength = 0;
      attr.$observe('minlength', function(value) {
        minlength = toInt(value) || 0;
        ctrl.$validate();
      });
      ctrl.$validators.minlength = function(modelValue, viewValue) {
        return ctrl.$isEmpty(viewValue) || viewValue.length >= minlength;
      };
    }
  };
};

if (window.angular.bootstrap) {
  // AngularJS is already loaded, so we can return here...
  if (window.console) {
    console.log('WARNING: Tried to load angular more than once.');
  }
  return;
}

// try to bind to jquery now so that one can write jqLite(fn)
// but we will rebind on bootstrap again.
bindJQuery();

publishExternalAPI(angular);

angular.module("ngLocale", [], ["$provide", function($provide) {
var PLURAL_CATEGORY = {ZERO: "zero", ONE: "one", TWO: "two", FEW: "few", MANY: "many", OTHER: "other"};
function getDecimals(n) {
  n = n + '';
  var i = n.indexOf('.');
  return (i == -1) ? 0 : n.length - i - 1;
}

function getVF(n, opt_precision) {
  var v = opt_precision;

  if (undefined === v) {
    v = Math.min(getDecimals(n), 3);
  }

  var base = Math.pow(10, v);
  var f = ((n * base) | 0) % base;
  return {v: v, f: f};
}

$provide.value("$locale", {
  "DATETIME_FORMATS": {
    "AMPMS": [
      "AM",
      "PM"
    ],
    "DAY": [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday"
    ],
    "ERANAMES": [
      "Before Christ",
      "Anno Domini"
    ],
    "ERAS": [
      "BC",
      "AD"
    ],
    "FIRSTDAYOFWEEK": 6,
    "MONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "SHORTDAY": [
      "Sun",
      "Mon",
      "Tue",
      "Wed",
      "Thu",
      "Fri",
      "Sat"
    ],
    "SHORTMONTH": [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ],
    "STANDALONEMONTH": [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ],
    "WEEKENDRANGE": [
      5,
      6
    ],
    "fullDate": "EEEE, MMMM d, y",
    "longDate": "MMMM d, y",
    "medium": "MMM d, y h:mm:ss a",
    "mediumDate": "MMM d, y",
    "mediumTime": "h:mm:ss a",
    "short": "M/d/yy h:mm a",
    "shortDate": "M/d/yy",
    "shortTime": "h:mm a"
  },
  "NUMBER_FORMATS": {
    "CURRENCY_SYM": "$",
    "DECIMAL_SEP": ".",
    "GROUP_SEP": ",",
    "PATTERNS": [
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 3,
        "minFrac": 0,
        "minInt": 1,
        "negPre": "-",
        "negSuf": "",
        "posPre": "",
        "posSuf": ""
      },
      {
        "gSize": 3,
        "lgSize": 3,
        "maxFrac": 2,
        "minFrac": 2,
        "minInt": 1,
        "negPre": "-\u00a4",
        "negSuf": "",
        "posPre": "\u00a4",
        "posSuf": ""
      }
    ]
  },
  "id": "en-us",
  "localeID": "en_US",
  "pluralCat": function(n, opt_precision) {  var i = n | 0;  var vf = getVF(n, opt_precision);  if (i == 1 && vf.v == 0) {    return PLURAL_CATEGORY.ONE;  }  return PLURAL_CATEGORY.OTHER;}
});
}]);

  jqLite(function() {
    angularInit(window.document, bootstrap);
  });

})(window);

!window.angular.$$csp().noInlineStyle && window.angular.element(document.head).prepend('<style type="text/css">@charset "UTF-8";[ng\\:cloak],[ng-cloak],[data-ng-cloak],[x-ng-cloak],.ng-cloak,.x-ng-cloak,.ng-hide:not(.ng-hide-animate){display:none !important;}ng\\:form{display:block;}.ng-animate-shim{visibility:hidden;}.ng-anchor{position:absolute;}</style>');
/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var ELEMENT_NODE = 1;
var COMMENT_NODE = 8;

var ADD_CLASS_SUFFIX = '-add';
var REMOVE_CLASS_SUFFIX = '-remove';
var EVENT_CLASS_PREFIX = 'ng-';
var ACTIVE_CLASS_SUFFIX = '-active';
var PREPARE_CLASS_SUFFIX = '-prepare';

var NG_ANIMATE_CLASSNAME = 'ng-animate';
var NG_ANIMATE_CHILDREN_DATA = '$$ngAnimateChildren';

// Detect proper transitionend/animationend event names.
var CSS_PREFIX = '', TRANSITION_PROP, TRANSITIONEND_EVENT, ANIMATION_PROP, ANIMATIONEND_EVENT;

// If unprefixed events are not supported but webkit-prefixed are, use the latter.
// Otherwise, just use W3C names, browsers not supporting them at all will just ignore them.
// Note: Chrome implements `window.onwebkitanimationend` and doesn't implement `window.onanimationend`
// but at the same time dispatches the `animationend` event and not `webkitAnimationEnd`.
// Register both events in case `window.onanimationend` is not supported because of that,
// do the same for `transitionend` as Safari is likely to exhibit similar behavior.
// Also, the only modern browser that uses vendor prefixes for transitions/keyframes is webkit
// therefore there is no reason to test anymore for other vendor prefixes:
// http://caniuse.com/#search=transition
if ((window.ontransitionend === undefined) && (window.onwebkittransitionend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  TRANSITION_PROP = 'WebkitTransition';
  TRANSITIONEND_EVENT = 'webkitTransitionEnd transitionend';
} else {
  TRANSITION_PROP = 'transition';
  TRANSITIONEND_EVENT = 'transitionend';
}

if ((window.onanimationend === undefined) && (window.onwebkitanimationend !== undefined)) {
  CSS_PREFIX = '-webkit-';
  ANIMATION_PROP = 'WebkitAnimation';
  ANIMATIONEND_EVENT = 'webkitAnimationEnd animationend';
} else {
  ANIMATION_PROP = 'animation';
  ANIMATIONEND_EVENT = 'animationend';
}

var DURATION_KEY = 'Duration';
var PROPERTY_KEY = 'Property';
var DELAY_KEY = 'Delay';
var TIMING_KEY = 'TimingFunction';
var ANIMATION_ITERATION_COUNT_KEY = 'IterationCount';
var ANIMATION_PLAYSTATE_KEY = 'PlayState';
var SAFE_FAST_FORWARD_DURATION_VALUE = 9999;

var ANIMATION_DELAY_PROP = ANIMATION_PROP + DELAY_KEY;
var ANIMATION_DURATION_PROP = ANIMATION_PROP + DURATION_KEY;
var TRANSITION_DELAY_PROP = TRANSITION_PROP + DELAY_KEY;
var TRANSITION_DURATION_PROP = TRANSITION_PROP + DURATION_KEY;

var ngMinErr = angular.$$minErr('ng');
function assertArg(arg, name, reason) {
  if (!arg) {
    throw ngMinErr('areq', 'Argument \'{0}\' is {1}', (name || '?'), (reason || 'required'));
  }
  return arg;
}

function mergeClasses(a,b) {
  if (!a && !b) return '';
  if (!a) return b;
  if (!b) return a;
  if (isArray(a)) a = a.join(' ');
  if (isArray(b)) b = b.join(' ');
  return a + ' ' + b;
}

function packageStyles(options) {
  var styles = {};
  if (options && (options.to || options.from)) {
    styles.to = options.to;
    styles.from = options.from;
  }
  return styles;
}

function pendClasses(classes, fix, isPrefix) {
  var className = '';
  classes = isArray(classes)
      ? classes
      : classes && isString(classes) && classes.length
          ? classes.split(/\s+/)
          : [];
  forEach(classes, function(klass, i) {
    if (klass && klass.length > 0) {
      className += (i > 0) ? ' ' : '';
      className += isPrefix ? fix + klass
                            : klass + fix;
    }
  });
  return className;
}

function removeFromArray(arr, val) {
  var index = arr.indexOf(val);
  if (val >= 0) {
    arr.splice(index, 1);
  }
}

function stripCommentsFromElement(element) {
  if (element instanceof jqLite) {
    switch (element.length) {
      case 0:
        return element;

      case 1:
        // there is no point of stripping anything if the element
        // is the only element within the jqLite wrapper.
        // (it's important that we retain the element instance.)
        if (element[0].nodeType === ELEMENT_NODE) {
          return element;
        }
        break;

      default:
        return jqLite(extractElementNode(element));
    }
  }

  if (element.nodeType === ELEMENT_NODE) {
    return jqLite(element);
  }
}

function extractElementNode(element) {
  if (!element[0]) return element;
  for (var i = 0; i < element.length; i++) {
    var elm = element[i];
    if (elm.nodeType === ELEMENT_NODE) {
      return elm;
    }
  }
}

function $$addClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.addClass(elm, className);
  });
}

function $$removeClass($$jqLite, element, className) {
  forEach(element, function(elm) {
    $$jqLite.removeClass(elm, className);
  });
}

function applyAnimationClassesFactory($$jqLite) {
  return function(element, options) {
    if (options.addClass) {
      $$addClass($$jqLite, element, options.addClass);
      options.addClass = null;
    }
    if (options.removeClass) {
      $$removeClass($$jqLite, element, options.removeClass);
      options.removeClass = null;
    }
  };
}

function prepareAnimationOptions(options) {
  options = options || {};
  if (!options.$$prepared) {
    var domOperation = options.domOperation || noop;
    options.domOperation = function() {
      options.$$domOperationFired = true;
      domOperation();
      domOperation = noop;
    };
    options.$$prepared = true;
  }
  return options;
}

function applyAnimationStyles(element, options) {
  applyAnimationFromStyles(element, options);
  applyAnimationToStyles(element, options);
}

function applyAnimationFromStyles(element, options) {
  if (options.from) {
    element.css(options.from);
    options.from = null;
  }
}

function applyAnimationToStyles(element, options) {
  if (options.to) {
    element.css(options.to);
    options.to = null;
  }
}

function mergeAnimationDetails(element, oldAnimation, newAnimation) {
  var target = oldAnimation.options || {};
  var newOptions = newAnimation.options || {};

  var toAdd = (target.addClass || '') + ' ' + (newOptions.addClass || '');
  var toRemove = (target.removeClass || '') + ' ' + (newOptions.removeClass || '');
  var classes = resolveElementClasses(element.attr('class'), toAdd, toRemove);

  if (newOptions.preparationClasses) {
    target.preparationClasses = concatWithSpace(newOptions.preparationClasses, target.preparationClasses);
    delete newOptions.preparationClasses;
  }

  // noop is basically when there is no callback; otherwise something has been set
  var realDomOperation = target.domOperation !== noop ? target.domOperation : null;

  extend(target, newOptions);

  // TODO(matsko or sreeramu): proper fix is to maintain all animation callback in array and call at last,but now only leave has the callback so no issue with this.
  if (realDomOperation) {
    target.domOperation = realDomOperation;
  }

  if (classes.addClass) {
    target.addClass = classes.addClass;
  } else {
    target.addClass = null;
  }

  if (classes.removeClass) {
    target.removeClass = classes.removeClass;
  } else {
    target.removeClass = null;
  }

  oldAnimation.addClass = target.addClass;
  oldAnimation.removeClass = target.removeClass;

  return target;
}

function resolveElementClasses(existing, toAdd, toRemove) {
  var ADD_CLASS = 1;
  var REMOVE_CLASS = -1;

  var flags = {};
  existing = splitClassesToLookup(existing);

  toAdd = splitClassesToLookup(toAdd);
  forEach(toAdd, function(value, key) {
    flags[key] = ADD_CLASS;
  });

  toRemove = splitClassesToLookup(toRemove);
  forEach(toRemove, function(value, key) {
    flags[key] = flags[key] === ADD_CLASS ? null : REMOVE_CLASS;
  });

  var classes = {
    addClass: '',
    removeClass: ''
  };

  forEach(flags, function(val, klass) {
    var prop, allow;
    if (val === ADD_CLASS) {
      prop = 'addClass';
      allow = !existing[klass] || existing[klass + REMOVE_CLASS_SUFFIX];
    } else if (val === REMOVE_CLASS) {
      prop = 'removeClass';
      allow = existing[klass] || existing[klass + ADD_CLASS_SUFFIX];
    }
    if (allow) {
      if (classes[prop].length) {
        classes[prop] += ' ';
      }
      classes[prop] += klass;
    }
  });

  function splitClassesToLookup(classes) {
    if (isString(classes)) {
      classes = classes.split(' ');
    }

    var obj = {};
    forEach(classes, function(klass) {
      // sometimes the split leaves empty string values
      // incase extra spaces were applied to the options
      if (klass.length) {
        obj[klass] = true;
      }
    });
    return obj;
  }

  return classes;
}

function getDomNode(element) {
  return (element instanceof jqLite) ? element[0] : element;
}

function applyGeneratedPreparationClasses($$jqLite, element, event, options) {
  var classes = '';
  if (event) {
    classes = pendClasses(event, EVENT_CLASS_PREFIX, true);
  }
  if (options.addClass) {
    classes = concatWithSpace(classes, pendClasses(options.addClass, ADD_CLASS_SUFFIX));
  }
  if (options.removeClass) {
    classes = concatWithSpace(classes, pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX));
  }
  if (classes.length) {
    options.preparationClasses = classes;
    element.addClass(classes);
  }
}

function clearGeneratedClasses(element, options) {
  if (options.preparationClasses) {
    element.removeClass(options.preparationClasses);
    options.preparationClasses = null;
  }
  if (options.activeClasses) {
    element.removeClass(options.activeClasses);
    options.activeClasses = null;
  }
}

function blockKeyframeAnimations(node, applyBlock) {
  var value = applyBlock ? 'paused' : '';
  var key = ANIMATION_PROP + ANIMATION_PLAYSTATE_KEY;
  applyInlineStyle(node, [key, value]);
  return [key, value];
}

function applyInlineStyle(node, styleTuple) {
  var prop = styleTuple[0];
  var value = styleTuple[1];
  node.style[prop] = value;
}

function concatWithSpace(a,b) {
  if (!a) return b;
  if (!b) return a;
  return a + ' ' + b;
}

var helpers = {
  blockTransitions: function(node, duration) {
    // we use a negative delay value since it performs blocking
    // yet it doesn't kill any existing transitions running on the
    // same element which makes this safe for class-based animations
    var value = duration ? '-' + duration + 's' : '';
    applyInlineStyle(node, [TRANSITION_DELAY_PROP, value]);
    return [TRANSITION_DELAY_PROP, value];
  }
};

var $$rAFSchedulerFactory = ['$$rAF', function($$rAF) {
  var queue, cancelFn;

  function scheduler(tasks) {
    // we make a copy since RAFScheduler mutates the state
    // of the passed in array variable and this would be difficult
    // to track down on the outside code
    queue = queue.concat(tasks);
    nextTick();
  }

  queue = scheduler.queue = [];

  /* waitUntilQuiet does two things:
   * 1. It will run the FINAL `fn` value only when an uncanceled RAF has passed through
   * 2. It will delay the next wave of tasks from running until the quiet `fn` has run.
   *
   * The motivation here is that animation code can request more time from the scheduler
   * before the next wave runs. This allows for certain DOM properties such as classes to
   * be resolved in time for the next animation to run.
   */
  scheduler.waitUntilQuiet = function(fn) {
    if (cancelFn) cancelFn();

    cancelFn = $$rAF(function() {
      cancelFn = null;
      fn();
      nextTick();
    });
  };

  return scheduler;

  function nextTick() {
    if (!queue.length) return;

    var items = queue.shift();
    for (var i = 0; i < items.length; i++) {
      items[i]();
    }

    if (!cancelFn) {
      $$rAF(function() {
        if (!cancelFn) nextTick();
      });
    }
  }
}];

/**
 * @ngdoc directive
 * @name ngAnimateChildren
 * @restrict AE
 * @element ANY
 *
 * @description
 *
 * ngAnimateChildren allows you to specify that children of this element should animate even if any
 * of the children's parents are currently animating. By default, when an element has an active `enter`, `leave`, or `move`
 * (structural) animation, child elements that also have an active structural animation are not animated.
 *
 * Note that even if `ngAnimateChildren` is set, no child animations will run when the parent element is removed from the DOM (`leave` animation).
 *
 *
 * @param {string} ngAnimateChildren If the value is empty, `true` or `on`,
 *     then child animations are allowed. If the value is `false`, child animations are not allowed.
 *
 * @example
 * <example module="ngAnimateChildren" name="ngAnimateChildren" deps="angular-animate.js" animations="true">
     <file name="index.html">
       <div ng-controller="MainController as main">
         <label>Show container? <input type="checkbox" ng-model="main.enterElement" /></label>
         <label>Animate children? <input type="checkbox" ng-model="main.animateChildren" /></label>
         <hr>
         <div ng-animate-children="{{main.animateChildren}}">
           <div ng-if="main.enterElement" class="container">
             List of items:
             <div ng-repeat="item in [0, 1, 2, 3]" class="item">Item {{item}}</div>
           </div>
         </div>
       </div>
     </file>
     <file name="animations.css">

      .container.ng-enter,
      .container.ng-leave {
        transition: all ease 1.5s;
      }

      .container.ng-enter,
      .container.ng-leave-active {
        opacity: 0;
      }

      .container.ng-leave,
      .container.ng-enter-active {
        opacity: 1;
      }

      .item {
        background: firebrick;
        color: #FFF;
        margin-bottom: 10px;
      }

      .item.ng-enter,
      .item.ng-leave {
        transition: transform 1.5s ease;
      }

      .item.ng-enter {
        transform: translateX(50px);
      }

      .item.ng-enter-active {
        transform: translateX(0);
      }
    </file>
    <file name="script.js">
      angular.module('ngAnimateChildren', ['ngAnimate'])
        .controller('MainController', function MainController() {
          this.animateChildren = false;
          this.enterElement = false;
        });
    </file>
  </example>
 */
var $$AnimateChildrenDirective = ['$interpolate', function($interpolate) {
  return {
    link: function(scope, element, attrs) {
      var val = attrs.ngAnimateChildren;
      if (isString(val) && val.length === 0) { //empty attribute
        element.data(NG_ANIMATE_CHILDREN_DATA, true);
      } else {
        // Interpolate and set the value, so that it is available to
        // animations that run right after compilation
        setData($interpolate(val)(scope));
        attrs.$observe('ngAnimateChildren', setData);
      }

      function setData(value) {
        value = value === 'on' || value === 'true';
        element.data(NG_ANIMATE_CHILDREN_DATA, value);
      }
    }
  };
}];

/* exported $AnimateCssProvider */

var ANIMATE_TIMER_KEY = '$$animateCss';

/**
 * @ngdoc service
 * @name $animateCss
 * @kind object
 *
 * @description
 * The `$animateCss` service is a useful utility to trigger customized CSS-based transitions/keyframes
 * from a JavaScript-based animation or directly from a directive. The purpose of `$animateCss` is NOT
 * to side-step how `$animate` and ngAnimate work, but the goal is to allow pre-existing animations or
 * directives to create more complex animations that can be purely driven using CSS code.
 *
 * Note that only browsers that support CSS transitions and/or keyframe animations are capable of
 * rendering animations triggered via `$animateCss` (bad news for IE9 and lower).
 *
 * ## General Use
 * Once again, `$animateCss` is designed to be used inside of a registered JavaScript animation that
 * is powered by ngAnimate. It is possible to use `$animateCss` directly inside of a directive, however,
 * any automatic control over cancelling animations and/or preventing animations from being run on
 * child elements will not be handled by AngularJS. For this to work as expected, please use `$animate` to
 * trigger the animation and then setup a JavaScript animation that injects `$animateCss` to trigger
 * the CSS animation.
 *
 * The example below shows how we can create a folding animation on an element using `ng-if`:
 *
 * ```html
 * <!-- notice the `fold-animation` CSS class -->
 * <div ng-if="onOff" class="fold-animation">
 *   This element will go BOOM
 * </div>
 * <button ng-click="onOff=true">Fold In</button>
 * ```
 *
 * Now we create the **JavaScript animation** that will trigger the CSS transition:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * ## More Advanced Uses
 *
 * `$animateCss` is the underlying code that ngAnimate uses to power **CSS-based animations** behind the scenes. Therefore CSS hooks
 * like `.ng-EVENT`, `.ng-EVENT-active`, `.ng-EVENT-stagger` are all features that can be triggered using `$animateCss` via JavaScript code.
 *
 * This also means that just about any combination of adding classes, removing classes, setting styles, dynamically setting a keyframe animation,
 * applying a hardcoded duration or delay value, changing the animation easing or applying a stagger animation are all options that work with
 * `$animateCss`. The service itself is smart enough to figure out the combination of options and examine the element styling properties in order
 * to provide a working animation that will run in CSS.
 *
 * The example below showcases a more advanced version of the `.fold-animation` from the example above:
 *
 * ```js
 * ngModule.animation('.fold-animation', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element, doneFn) {
 *       var height = element[0].offsetHeight;
 *       return $animateCss(element, {
 *         addClass: 'red large-text pulse-twice',
 *         easing: 'ease-out',
 *         from: { height:'0px' },
 *         to: { height:height + 'px' },
 *         duration: 1 // one second
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Since we're adding/removing CSS classes then the CSS transition will also pick those up:
 *
 * ```css
 * /&#42; since a hardcoded duration value of 1 was provided in the JavaScript animation code,
 * the CSS classes below will be transitioned despite them being defined as regular CSS classes &#42;/
 * .red { background:red; }
 * .large-text { font-size:20px; }
 *
 * /&#42; we can also use a keyframe animation and $animateCss will make it work alongside the transition &#42;/
 * .pulse-twice {
 *   animation: 0.5s pulse linear 2;
 *   -webkit-animation: 0.5s pulse linear 2;
 * }
 *
 * @keyframes pulse {
 *   from { transform: scale(0.5); }
 *   to { transform: scale(1.5); }
 * }
 *
 * @-webkit-keyframes pulse {
 *   from { -webkit-transform: scale(0.5); }
 *   to { -webkit-transform: scale(1.5); }
 * }
 * ```
 *
 * Given this complex combination of CSS classes, styles and options, `$animateCss` will figure everything out and make the animation happen.
 *
 * ## How the Options are handled
 *
 * `$animateCss` is very versatile and intelligent when it comes to figuring out what configurations to apply to the element to ensure the animation
 * works with the options provided. Say for example we were adding a class that contained a keyframe value and we wanted to also animate some inline
 * styles using the `from` and `to` properties.
 *
 * ```js
 * var animator = $animateCss(element, {
 *   from: { background:'red' },
 *   to: { background:'blue' }
 * });
 * animator.start();
 * ```
 *
 * ```css
 * .rotating-animation {
 *   animation:0.5s rotate linear;
 *   -webkit-animation:0.5s rotate linear;
 * }
 *
 * @keyframes rotate {
 *   from { transform: rotate(0deg); }
 *   to { transform: rotate(360deg); }
 * }
 *
 * @-webkit-keyframes rotate {
 *   from { -webkit-transform: rotate(0deg); }
 *   to { -webkit-transform: rotate(360deg); }
 * }
 * ```
 *
 * The missing pieces here are that we do not have a transition set (within the CSS code nor within the `$animateCss` options) and the duration of the animation is
 * going to be detected from what the keyframe styles on the CSS class are. In this event, `$animateCss` will automatically create an inline transition
 * style matching the duration detected from the keyframe style (which is present in the CSS class that is being added) and then prepare both the transition
 * and keyframe animations to run in parallel on the element. Then when the animation is underway the provided `from` and `to` CSS styles will be applied
 * and spread across the transition and keyframe animation.
 *
 * ## What is returned
 *
 * `$animateCss` works in two stages: a preparation phase and an animation phase. Therefore when `$animateCss` is first called it will NOT actually
 * start the animation. All that is going on here is that the element is being prepared for the animation (which means that the generated CSS classes are
 * added and removed on the element). Once `$animateCss` is called it will return an object with the following properties:
 *
 * ```js
 * var animator = $animateCss(element, { ... });
 * ```
 *
 * Now what do the contents of our `animator` variable look like:
 *
 * ```js
 * {
 *   // starts the animation
 *   start: Function,
 *
 *   // ends (aborts) the animation
 *   end: Function
 * }
 * ```
 *
 * To actually start the animation we need to run `animation.start()` which will then return a promise that we can hook into to detect when the animation ends.
 * If we choose not to run the animation then we MUST run `animation.end()` to perform a cleanup on the element (since some CSS classes and styles may have been
 * applied to the element during the preparation phase). Note that all other properties such as duration, delay, transitions and keyframes are just properties
 * and that changing them will not reconfigure the parameters of the animation.
 *
 * ### runner.done() vs runner.then()
 * It is documented that `animation.start()` will return a promise object and this is true, however, there is also an additional method available on the
 * runner called `.done(callbackFn)`. The done method works the same as `.finally(callbackFn)`, however, it does **not trigger a digest to occur**.
 * Therefore, for performance reasons, it's always best to use `runner.done(callback)` instead of `runner.then()`, `runner.catch()` or `runner.finally()`
 * unless you really need a digest to kick off afterwards.
 *
 * Keep in mind that, to make this easier, ngAnimate has tweaked the JS animations API to recognize when a runner instance is returned from $animateCss
 * (so there is no need to call `runner.done(doneFn)` inside of your JavaScript animation code).
 * Check the {@link ngAnimate.$animateCss#usage animation code above} to see how this works.
 *
 * @param {DOMElement} element the element that will be animated
 * @param {object} options the animation-related options that will be applied during the animation
 *
 * * `event` - The DOM event (e.g. enter, leave, move). When used, a generated CSS class of `ng-EVENT` and `ng-EVENT-active` will be applied
 * to the element during the animation. Multiple events can be provided when spaces are used as a separator. (Note that this will not perform any DOM operation.)
 * * `structural` - Indicates that the `ng-` prefix will be added to the event class. Setting to `false` or omitting will turn `ng-EVENT` and
 * `ng-EVENT-active` in `EVENT` and `EVENT-active`. Unused if `event` is omitted.
 * * `easing` - The CSS easing value that will be applied to the transition or keyframe animation (or both).
 * * `transitionStyle` - The raw CSS transition style that will be used (e.g. `1s linear all`).
 * * `keyframeStyle` - The raw CSS keyframe animation style that will be used (e.g. `1s my_animation linear`).
 * * `from` - The starting CSS styles (a key/value object) that will be applied at the start of the animation.
 * * `to` - The ending CSS styles (a key/value object) that will be applied across the animation via a CSS transition.
 * * `addClass` - A space separated list of CSS classes that will be added to the element and spread across the animation.
 * * `removeClass` - A space separated list of CSS classes that will be removed from the element and spread across the animation.
 * * `duration` - A number value representing the total duration of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `0`
 * is provided then the animation will be skipped entirely.
 * * `delay` - A number value representing the total delay of the transition and/or keyframe (note that a value of 1 is 1000ms). If a value of `true` is
 * used then whatever delay value is detected from the CSS classes will be mirrored on the elements styles (e.g. by setting delay true then the style value
 * of the element will be `transition-delay: DETECTED_VALUE`). Using `true` is useful when you want the CSS classes and inline styles to all share the same
 * CSS delay value.
 * * `stagger` - A numeric time value representing the delay between successively animated elements
 * ({@link ngAnimate#css-staggering-animations Click here to learn how CSS-based staggering works in ngAnimate.})
 * * `staggerIndex` - The numeric index representing the stagger item (e.g. a value of 5 is equal to the sixth item in the stagger; therefore when a
 *   `stagger` option value of `0.1` is used then there will be a stagger delay of `600ms`)
 * * `applyClassesEarly` - Whether or not the classes being added or removed will be used when detecting the animation. This is set by `$animate` when enter/leave/move animations are fired to ensure that the CSS classes are resolved in time. (Note that this will prevent any transitions from occurring on the classes being added and removed.)
 * * `cleanupStyles` - Whether or not the provided `from` and `to` styles will be removed once
 *    the animation is closed. This is useful for when the styles are used purely for the sake of
 *    the animation and do not have a lasting visual effect on the element (e.g. a collapse and open animation).
 *    By default this value is set to `false`.
 *
 * @return {object} an object with start and end methods and details about the animation.
 *
 * * `start` - The method to start the animation. This will return a `Promise` when called.
 * * `end` - This method will cancel the animation and remove all applied CSS classes and styles.
 */
var ONE_SECOND = 1000;

var ELAPSED_TIME_MAX_DECIMAL_PLACES = 3;
var CLOSING_TIME_BUFFER = 1.5;

var DETECT_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  transitionProperty:      TRANSITION_PROP + PROPERTY_KEY,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP,
  animationIterationCount: ANIMATION_PROP + ANIMATION_ITERATION_COUNT_KEY
};

var DETECT_STAGGER_CSS_PROPERTIES = {
  transitionDuration:      TRANSITION_DURATION_PROP,
  transitionDelay:         TRANSITION_DELAY_PROP,
  animationDuration:       ANIMATION_DURATION_PROP,
  animationDelay:          ANIMATION_DELAY_PROP
};

function getCssKeyframeDurationStyle(duration) {
  return [ANIMATION_DURATION_PROP, duration + 's'];
}

function getCssDelayStyle(delay, isKeyframeAnimation) {
  var prop = isKeyframeAnimation ? ANIMATION_DELAY_PROP : TRANSITION_DELAY_PROP;
  return [prop, delay + 's'];
}

function computeCssStyles($window, element, properties) {
  var styles = Object.create(null);
  var detectedStyles = $window.getComputedStyle(element) || {};
  forEach(properties, function(formalStyleName, actualStyleName) {
    var val = detectedStyles[formalStyleName];
    if (val) {
      var c = val.charAt(0);

      // only numerical-based values have a negative sign or digit as the first value
      if (c === '-' || c === '+' || c >= 0) {
        val = parseMaxTime(val);
      }

      // by setting this to null in the event that the delay is not set or is set directly as 0
      // then we can still allow for negative values to be used later on and not mistake this
      // value for being greater than any other negative value.
      if (val === 0) {
        val = null;
      }
      styles[actualStyleName] = val;
    }
  });

  return styles;
}

function parseMaxTime(str) {
  var maxValue = 0;
  var values = str.split(/\s*,\s*/);
  forEach(values, function(value) {
    // it's always safe to consider only second values and omit `ms` values since
    // getComputedStyle will always handle the conversion for us
    if (value.charAt(value.length - 1) === 's') {
      value = value.substring(0, value.length - 1);
    }
    value = parseFloat(value) || 0;
    maxValue = maxValue ? Math.max(value, maxValue) : value;
  });
  return maxValue;
}

function truthyTimingValue(val) {
  return val === 0 || val != null;
}

function getCssTransitionDurationStyle(duration, applyOnlyDuration) {
  var style = TRANSITION_PROP;
  var value = duration + 's';
  if (applyOnlyDuration) {
    style += DURATION_KEY;
  } else {
    value += ' linear all';
  }
  return [style, value];
}

// we do not reassign an already present style value since
// if we detect the style property value again we may be
// detecting styles that were added via the `from` styles.
// We make use of `isDefined` here since an empty string
// or null value (which is what getPropertyValue will return
// for a non-existing style) will still be marked as a valid
// value for the style (a falsy value implies that the style
// is to be removed at the end of the animation). If we had a simple
// "OR" statement then it would not be enough to catch that.
function registerRestorableStyles(backup, node, properties) {
  forEach(properties, function(prop) {
    backup[prop] = isDefined(backup[prop])
        ? backup[prop]
        : node.style.getPropertyValue(prop);
  });
}

var $AnimateCssProvider = ['$animateProvider', /** @this */ function($animateProvider) {

  this.$get = ['$window', '$$jqLite', '$$AnimateRunner', '$timeout', '$$animateCache',
               '$$forceReflow', '$sniffer', '$$rAFScheduler', '$$animateQueue',
       function($window,   $$jqLite,   $$AnimateRunner,   $timeout,   $$animateCache,
                $$forceReflow,   $sniffer,   $$rAFScheduler, $$animateQueue) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function computeCachedCssStyles(node, className, cacheKey, allowNoDuration, properties) {
      var timings = $$animateCache.get(cacheKey);

      if (!timings) {
        timings = computeCssStyles($window, node, properties);
        if (timings.animationIterationCount === 'infinite') {
          timings.animationIterationCount = 1;
        }
      }

      // if a css animation has no duration we
      // should mark that so that repeated addClass/removeClass calls are skipped
      var hasDuration = allowNoDuration || (timings.transitionDuration > 0 || timings.animationDuration > 0);

      // we keep putting this in multiple times even though the value and the cacheKey are the same
      // because we're keeping an internal tally of how many duplicate animations are detected.
      $$animateCache.put(cacheKey, timings, hasDuration);

      return timings;
    }

    function computeCachedCssStaggerStyles(node, className, cacheKey, properties) {
      var stagger;
      var staggerCacheKey = 'stagger-' + cacheKey;

      // if we have one or more existing matches of matching elements
      // containing the same parent + CSS styles (which is how cacheKey works)
      // then staggering is possible
      if ($$animateCache.count(cacheKey) > 0) {
        stagger = $$animateCache.get(staggerCacheKey);

        if (!stagger) {
          var staggerClassName = pendClasses(className, '-stagger');

          $$jqLite.addClass(node, staggerClassName);

          stagger = computeCssStyles($window, node, properties);

          // force the conversion of a null value to zero incase not set
          stagger.animationDuration = Math.max(stagger.animationDuration, 0);
          stagger.transitionDuration = Math.max(stagger.transitionDuration, 0);

          $$jqLite.removeClass(node, staggerClassName);

          $$animateCache.put(staggerCacheKey, stagger, true);
        }
      }

      return stagger || {};
    }

    var rafWaitQueue = [];
    function waitUntilQuiet(callback) {
      rafWaitQueue.push(callback);
      $$rAFScheduler.waitUntilQuiet(function() {
        $$animateCache.flush();

        // DO NOT REMOVE THIS LINE OR REFACTOR OUT THE `pageWidth` variable.
        // PLEASE EXAMINE THE `$$forceReflow` service to understand why.
        var pageWidth = $$forceReflow();

        // we use a for loop to ensure that if the queue is changed
        // during this looping then it will consider new requests
        for (var i = 0; i < rafWaitQueue.length; i++) {
          rafWaitQueue[i](pageWidth);
        }
        rafWaitQueue.length = 0;
      });
    }

    function computeTimings(node, className, cacheKey, allowNoDuration) {
      var timings = computeCachedCssStyles(node, className, cacheKey, allowNoDuration, DETECT_CSS_PROPERTIES);
      var aD = timings.animationDelay;
      var tD = timings.transitionDelay;
      timings.maxDelay = aD && tD
          ? Math.max(aD, tD)
          : (aD || tD);
      timings.maxDuration = Math.max(
          timings.animationDuration * timings.animationIterationCount,
          timings.transitionDuration);

      return timings;
    }

    return function init(element, initialOptions) {
      // all of the animation functions should create
      // a copy of the options data, however, if a
      // parent service has already created a copy then
      // we should stick to using that
      var options = initialOptions || {};
      if (!options.$$prepared) {
        options = prepareAnimationOptions(copy(options));
      }

      var restoreStyles = {};
      var node = getDomNode(element);
      if (!node
          || !node.parentNode
          || !$$animateQueue.enabled()) {
        return closeAndReturnNoopAnimator();
      }

      var temporaryStyles = [];
      var classes = element.attr('class');
      var styles = packageStyles(options);
      var animationClosed;
      var animationPaused;
      var animationCompleted;
      var runner;
      var runnerHost;
      var maxDelay;
      var maxDelayTime;
      var maxDuration;
      var maxDurationTime;
      var startTime;
      var events = [];

      if (options.duration === 0 || (!$sniffer.animations && !$sniffer.transitions)) {
        return closeAndReturnNoopAnimator();
      }

      var method = options.event && isArray(options.event)
            ? options.event.join(' ')
            : options.event;

      var isStructural = method && options.structural;
      var structuralClassName = '';
      var addRemoveClassName = '';

      if (isStructural) {
        structuralClassName = pendClasses(method, EVENT_CLASS_PREFIX, true);
      } else if (method) {
        structuralClassName = method;
      }

      if (options.addClass) {
        addRemoveClassName += pendClasses(options.addClass, ADD_CLASS_SUFFIX);
      }

      if (options.removeClass) {
        if (addRemoveClassName.length) {
          addRemoveClassName += ' ';
        }
        addRemoveClassName += pendClasses(options.removeClass, REMOVE_CLASS_SUFFIX);
      }

      // there may be a situation where a structural animation is combined together
      // with CSS classes that need to resolve before the animation is computed.
      // However this means that there is no explicit CSS code to block the animation
      // from happening (by setting 0s none in the class name). If this is the case
      // we need to apply the classes before the first rAF so we know to continue if
      // there actually is a detected transition or keyframe animation
      if (options.applyClassesEarly && addRemoveClassName.length) {
        applyAnimationClasses(element, options);
      }

      var preparationClasses = [structuralClassName, addRemoveClassName].join(' ').trim();
      var fullClassName = classes + ' ' + preparationClasses;
      var hasToStyles = styles.to && Object.keys(styles.to).length > 0;
      var containsKeyframeAnimation = (options.keyframeStyle || '').length > 0;

      // there is no way we can trigger an animation if no styles and
      // no classes are being applied which would then trigger a transition,
      // unless there a is raw keyframe value that is applied to the element.
      if (!containsKeyframeAnimation
           && !hasToStyles
           && !preparationClasses) {
        return closeAndReturnNoopAnimator();
      }

      var stagger, cacheKey = $$animateCache.cacheKey(node, method, options.addClass, options.removeClass);
      if ($$animateCache.containsCachedAnimationWithoutDuration(cacheKey)) {
        preparationClasses = null;
        return closeAndReturnNoopAnimator();
      }

      if (options.stagger > 0) {
        var staggerVal = parseFloat(options.stagger);
        stagger = {
          transitionDelay: staggerVal,
          animationDelay: staggerVal,
          transitionDuration: 0,
          animationDuration: 0
        };
      } else {
        stagger = computeCachedCssStaggerStyles(node, preparationClasses, cacheKey, DETECT_STAGGER_CSS_PROPERTIES);
      }

      if (!options.$$skipPreparationClasses) {
        $$jqLite.addClass(element, preparationClasses);
      }

      var applyOnlyDuration;

      if (options.transitionStyle) {
        var transitionStyle = [TRANSITION_PROP, options.transitionStyle];
        applyInlineStyle(node, transitionStyle);
        temporaryStyles.push(transitionStyle);
      }

      if (options.duration >= 0) {
        applyOnlyDuration = node.style[TRANSITION_PROP].length > 0;
        var durationStyle = getCssTransitionDurationStyle(options.duration, applyOnlyDuration);

        // we set the duration so that it will be picked up by getComputedStyle later
        applyInlineStyle(node, durationStyle);
        temporaryStyles.push(durationStyle);
      }

      if (options.keyframeStyle) {
        var keyframeStyle = [ANIMATION_PROP, options.keyframeStyle];
        applyInlineStyle(node, keyframeStyle);
        temporaryStyles.push(keyframeStyle);
      }

      var itemIndex = stagger
          ? options.staggerIndex >= 0
              ? options.staggerIndex
              : $$animateCache.count(cacheKey)
          : 0;

      var isFirst = itemIndex === 0;

      // this is a pre-emptive way of forcing the setup classes to be added and applied INSTANTLY
      // without causing any combination of transitions to kick in. By adding a negative delay value
      // it forces the setup class' transition to end immediately. We later then remove the negative
      // transition delay to allow for the transition to naturally do it's thing. The beauty here is
      // that if there is no transition defined then nothing will happen and this will also allow
      // other transitions to be stacked on top of each other without any chopping them out.
      if (isFirst && !options.skipBlocking) {
        helpers.blockTransitions(node, SAFE_FAST_FORWARD_DURATION_VALUE);
      }

      var timings = computeTimings(node, fullClassName, cacheKey, !isStructural);
      var relativeDelay = timings.maxDelay;
      maxDelay = Math.max(relativeDelay, 0);
      maxDuration = timings.maxDuration;

      var flags = {};
      flags.hasTransitions          = timings.transitionDuration > 0;
      flags.hasAnimations           = timings.animationDuration > 0;
      flags.hasTransitionAll        = flags.hasTransitions && timings.transitionProperty === 'all';
      flags.applyTransitionDuration = hasToStyles && (
                                        (flags.hasTransitions && !flags.hasTransitionAll)
                                         || (flags.hasAnimations && !flags.hasTransitions));
      flags.applyAnimationDuration  = options.duration && flags.hasAnimations;
      flags.applyTransitionDelay    = truthyTimingValue(options.delay) && (flags.applyTransitionDuration || flags.hasTransitions);
      flags.applyAnimationDelay     = truthyTimingValue(options.delay) && flags.hasAnimations;
      flags.recalculateTimingStyles = addRemoveClassName.length > 0;

      if (flags.applyTransitionDuration || flags.applyAnimationDuration) {
        maxDuration = options.duration ? parseFloat(options.duration) : maxDuration;

        if (flags.applyTransitionDuration) {
          flags.hasTransitions = true;
          timings.transitionDuration = maxDuration;
          applyOnlyDuration = node.style[TRANSITION_PROP + PROPERTY_KEY].length > 0;
          temporaryStyles.push(getCssTransitionDurationStyle(maxDuration, applyOnlyDuration));
        }

        if (flags.applyAnimationDuration) {
          flags.hasAnimations = true;
          timings.animationDuration = maxDuration;
          temporaryStyles.push(getCssKeyframeDurationStyle(maxDuration));
        }
      }

      if (maxDuration === 0 && !flags.recalculateTimingStyles) {
        return closeAndReturnNoopAnimator();
      }

      var activeClasses = pendClasses(preparationClasses, ACTIVE_CLASS_SUFFIX);

      if (options.delay != null) {
        var delayStyle;
        if (typeof options.delay !== 'boolean') {
          delayStyle = parseFloat(options.delay);
          // number in options.delay means we have to recalculate the delay for the closing timeout
          maxDelay = Math.max(delayStyle, 0);
        }

        if (flags.applyTransitionDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle));
        }

        if (flags.applyAnimationDelay) {
          temporaryStyles.push(getCssDelayStyle(delayStyle, true));
        }
      }

      // we need to recalculate the delay value since we used a pre-emptive negative
      // delay value and the delay value is required for the final event checking. This
      // property will ensure that this will happen after the RAF phase has passed.
      if (options.duration == null && timings.transitionDuration > 0) {
        flags.recalculateTimingStyles = flags.recalculateTimingStyles || isFirst;
      }

      maxDelayTime = maxDelay * ONE_SECOND;
      maxDurationTime = maxDuration * ONE_SECOND;
      if (!options.skipBlocking) {
        flags.blockTransition = timings.transitionDuration > 0;
        flags.blockKeyframeAnimation = timings.animationDuration > 0 &&
                                       stagger.animationDelay > 0 &&
                                       stagger.animationDuration === 0;
      }

      if (options.from) {
        if (options.cleanupStyles) {
          registerRestorableStyles(restoreStyles, node, Object.keys(options.from));
        }
        applyAnimationFromStyles(element, options);
      }

      if (flags.blockTransition || flags.blockKeyframeAnimation) {
        applyBlocking(maxDuration);
      } else if (!options.skipBlocking) {
        helpers.blockTransitions(node, false);
      }

      // TODO(matsko): for 1.5 change this code to have an animator object for better debugging
      return {
        $$willAnimate: true,
        end: endFn,
        start: function() {
          if (animationClosed) return;

          runnerHost = {
            end: endFn,
            cancel: cancelFn,
            resume: null, //this will be set during the start() phase
            pause: null
          };

          runner = new $$AnimateRunner(runnerHost);

          waitUntilQuiet(start);

          // we don't have access to pause/resume the animation
          // since it hasn't run yet. AnimateRunner will therefore
          // set noop functions for resume and pause and they will
          // later be overridden once the animation is triggered
          return runner;
        }
      };

      function endFn() {
        close();
      }

      function cancelFn() {
        close(true);
      }

      function close(rejected) {
        // if the promise has been called already then we shouldn't close
        // the animation again
        if (animationClosed || (animationCompleted && animationPaused)) return;
        animationClosed = true;
        animationPaused = false;

        if (preparationClasses && !options.$$skipPreparationClasses) {
          $$jqLite.removeClass(element, preparationClasses);
        }

        if (activeClasses) {
          $$jqLite.removeClass(element, activeClasses);
        }

        blockKeyframeAnimations(node, false);
        helpers.blockTransitions(node, false);

        forEach(temporaryStyles, function(entry) {
          // There is only one way to remove inline style properties entirely from elements.
          // By using `removeProperty` this works, but we need to convert camel-cased CSS
          // styles down to hyphenated values.
          node.style[entry[0]] = '';
        });

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);

        if (Object.keys(restoreStyles).length) {
          forEach(restoreStyles, function(value, prop) {
            if (value) {
              node.style.setProperty(prop, value);
            } else {
              node.style.removeProperty(prop);
            }
          });
        }

        // the reason why we have this option is to allow a synchronous closing callback
        // that is fired as SOON as the animation ends (when the CSS is removed) or if
        // the animation never takes off at all. A good example is a leave animation since
        // the element must be removed just after the animation is over or else the element
        // will appear on screen for one animation frame causing an overbearing flicker.
        if (options.onDone) {
          options.onDone();
        }

        if (events && events.length) {
          // Remove the transitionend / animationend listener(s)
          element.off(events.join(' '), onAnimationProgress);
        }

        //Cancel the fallback closing timeout and remove the timer data
        var animationTimerData = element.data(ANIMATE_TIMER_KEY);
        if (animationTimerData) {
          $timeout.cancel(animationTimerData[0].timer);
          element.removeData(ANIMATE_TIMER_KEY);
        }

        // if the preparation function fails then the promise is not setup
        if (runner) {
          runner.complete(!rejected);
        }
      }

      function applyBlocking(duration) {
        if (flags.blockTransition) {
          helpers.blockTransitions(node, duration);
        }

        if (flags.blockKeyframeAnimation) {
          blockKeyframeAnimations(node, !!duration);
        }
      }

      function closeAndReturnNoopAnimator() {
        runner = new $$AnimateRunner({
          end: endFn,
          cancel: cancelFn
        });

        // should flush the cache animation
        waitUntilQuiet(noop);
        close();

        return {
          $$willAnimate: false,
          start: function() {
            return runner;
          },
          end: endFn
        };
      }

      function onAnimationProgress(event) {
        event.stopPropagation();
        var ev = event.originalEvent || event;

        if (ev.target !== node) {
          // Since TransitionEvent / AnimationEvent bubble up,
          // we have to ignore events by finished child animations
          return;
        }

        // we now always use `Date.now()` due to the recent changes with
        // event.timeStamp in Firefox, Webkit and Chrome (see #13494 for more info)
        var timeStamp = ev.$manualTimeStamp || Date.now();

        /* Firefox (or possibly just Gecko) likes to not round values up
         * when a ms measurement is used for the animation */
        var elapsedTime = parseFloat(ev.elapsedTime.toFixed(ELAPSED_TIME_MAX_DECIMAL_PLACES));

        /* $manualTimeStamp is a mocked timeStamp value which is set
         * within browserTrigger(). This is only here so that tests can
         * mock animations properly. Real events fallback to event.timeStamp,
         * or, if they don't, then a timeStamp is automatically created for them.
         * We're checking to see if the timeStamp surpasses the expected delay,
         * but we're using elapsedTime instead of the timeStamp on the 2nd
         * pre-condition since animationPauseds sometimes close off early */
        if (Math.max(timeStamp - startTime, 0) >= maxDelayTime && elapsedTime >= maxDuration) {
          // we set this flag to ensure that if the transition is paused then, when resumed,
          // the animation will automatically close itself since transitions cannot be paused.
          animationCompleted = true;
          close();
        }
      }

      function start() {
        if (animationClosed) return;
        if (!node.parentNode) {
          close();
          return;
        }

        // even though we only pause keyframe animations here the pause flag
        // will still happen when transitions are used. Only the transition will
        // not be paused since that is not possible. If the animation ends when
        // paused then it will not complete until unpaused or cancelled.
        var playPause = function(playAnimation) {
          if (!animationCompleted) {
            animationPaused = !playAnimation;
            if (timings.animationDuration) {
              var value = blockKeyframeAnimations(node, animationPaused);
              if (animationPaused) {
                temporaryStyles.push(value);
              } else {
                removeFromArray(temporaryStyles, value);
              }
            }
          } else if (animationPaused && playAnimation) {
            animationPaused = false;
            close();
          }
        };

        // checking the stagger duration prevents an accidentally cascade of the CSS delay style
        // being inherited from the parent. If the transition duration is zero then we can safely
        // rely that the delay value is an intentional stagger delay style.
        var maxStagger = itemIndex > 0
                         && ((timings.transitionDuration && stagger.transitionDuration === 0) ||
                            (timings.animationDuration && stagger.animationDuration === 0))
                         && Math.max(stagger.animationDelay, stagger.transitionDelay);
        if (maxStagger) {
          $timeout(triggerAnimationStart,
                   Math.floor(maxStagger * itemIndex * ONE_SECOND),
                   false);
        } else {
          triggerAnimationStart();
        }

        // this will decorate the existing promise runner with pause/resume methods
        runnerHost.resume = function() {
          playPause(true);
        };

        runnerHost.pause = function() {
          playPause(false);
        };

        function triggerAnimationStart() {
          // just incase a stagger animation kicks in when the animation
          // itself was cancelled entirely
          if (animationClosed) return;

          applyBlocking(false);

          forEach(temporaryStyles, function(entry) {
            var key = entry[0];
            var value = entry[1];
            node.style[key] = value;
          });

          applyAnimationClasses(element, options);
          $$jqLite.addClass(element, activeClasses);

          if (flags.recalculateTimingStyles) {
            fullClassName = node.getAttribute('class') + ' ' + preparationClasses;
            cacheKey = $$animateCache.cacheKey(node, method, options.addClass, options.removeClass);

            timings = computeTimings(node, fullClassName, cacheKey, false);
            relativeDelay = timings.maxDelay;
            maxDelay = Math.max(relativeDelay, 0);
            maxDuration = timings.maxDuration;

            if (maxDuration === 0) {
              close();
              return;
            }

            flags.hasTransitions = timings.transitionDuration > 0;
            flags.hasAnimations = timings.animationDuration > 0;
          }

          if (flags.applyAnimationDelay) {
            relativeDelay = typeof options.delay !== 'boolean' && truthyTimingValue(options.delay)
                  ? parseFloat(options.delay)
                  : relativeDelay;

            maxDelay = Math.max(relativeDelay, 0);
            timings.animationDelay = relativeDelay;
            delayStyle = getCssDelayStyle(relativeDelay, true);
            temporaryStyles.push(delayStyle);
            node.style[delayStyle[0]] = delayStyle[1];
          }

          maxDelayTime = maxDelay * ONE_SECOND;
          maxDurationTime = maxDuration * ONE_SECOND;

          if (options.easing) {
            var easeProp, easeVal = options.easing;
            if (flags.hasTransitions) {
              easeProp = TRANSITION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
            if (flags.hasAnimations) {
              easeProp = ANIMATION_PROP + TIMING_KEY;
              temporaryStyles.push([easeProp, easeVal]);
              node.style[easeProp] = easeVal;
            }
          }

          if (timings.transitionDuration) {
            events.push(TRANSITIONEND_EVENT);
          }

          if (timings.animationDuration) {
            events.push(ANIMATIONEND_EVENT);
          }

          startTime = Date.now();
          var timerTime = maxDelayTime + CLOSING_TIME_BUFFER * maxDurationTime;
          var endTime = startTime + timerTime;

          var animationsData = element.data(ANIMATE_TIMER_KEY) || [];
          var setupFallbackTimer = true;
          if (animationsData.length) {
            var currentTimerData = animationsData[0];
            setupFallbackTimer = endTime > currentTimerData.expectedEndTime;
            if (setupFallbackTimer) {
              $timeout.cancel(currentTimerData.timer);
            } else {
              animationsData.push(close);
            }
          }

          if (setupFallbackTimer) {
            var timer = $timeout(onAnimationExpired, timerTime, false);
            animationsData[0] = {
              timer: timer,
              expectedEndTime: endTime
            };
            animationsData.push(close);
            element.data(ANIMATE_TIMER_KEY, animationsData);
          }

          if (events.length) {
            element.on(events.join(' '), onAnimationProgress);
          }

          if (options.to) {
            if (options.cleanupStyles) {
              registerRestorableStyles(restoreStyles, node, Object.keys(options.to));
            }
            applyAnimationToStyles(element, options);
          }
        }

        function onAnimationExpired() {
          var animationsData = element.data(ANIMATE_TIMER_KEY);

          // this will be false in the event that the element was
          // removed from the DOM (via a leave animation or something
          // similar)
          if (animationsData) {
            for (var i = 1; i < animationsData.length; i++) {
              animationsData[i]();
            }
            element.removeData(ANIMATE_TIMER_KEY);
          }
        }
      }
    };
  }];
}];

var $$AnimateCssDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateCssDriver');

  var NG_ANIMATE_SHIM_CLASS_NAME = 'ng-animate-shim';
  var NG_ANIMATE_ANCHOR_CLASS_NAME = 'ng-anchor';

  var NG_OUT_ANCHOR_CLASS_NAME = 'ng-anchor-out';
  var NG_IN_ANCHOR_CLASS_NAME = 'ng-anchor-in';

  function isDocumentFragment(node) {
    return node.parentNode && node.parentNode.nodeType === 11;
  }

  this.$get = ['$animateCss', '$rootScope', '$$AnimateRunner', '$rootElement', '$sniffer', '$$jqLite', '$document',
       function($animateCss,   $rootScope,   $$AnimateRunner,   $rootElement,   $sniffer,   $$jqLite,   $document) {

    // only browsers that support these properties can render animations
    if (!$sniffer.animations && !$sniffer.transitions) return noop;

    var bodyNode = $document[0].body;
    var rootNode = getDomNode($rootElement);

    var rootBodyElement = jqLite(
      // this is to avoid using something that exists outside of the body
      // we also special case the doc fragment case because our unit test code
      // appends the $rootElement to the body after the app has been bootstrapped
      isDocumentFragment(rootNode) || bodyNode.contains(rootNode) ? rootNode : bodyNode
    );

    return function initDriverFn(animationDetails) {
      return animationDetails.from && animationDetails.to
          ? prepareFromToAnchorAnimation(animationDetails.from,
                                         animationDetails.to,
                                         animationDetails.classes,
                                         animationDetails.anchors)
          : prepareRegularAnimation(animationDetails);
    };

    function filterCssClasses(classes) {
      //remove all the `ng-` stuff
      return classes.replace(/\bng-\S+\b/g, '');
    }

    function getUniqueValues(a, b) {
      if (isString(a)) a = a.split(' ');
      if (isString(b)) b = b.split(' ');
      return a.filter(function(val) {
        return b.indexOf(val) === -1;
      }).join(' ');
    }

    function prepareAnchoredAnimation(classes, outAnchor, inAnchor) {
      var clone = jqLite(getDomNode(outAnchor).cloneNode(true));
      var startingClasses = filterCssClasses(getClassVal(clone));

      outAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);
      inAnchor.addClass(NG_ANIMATE_SHIM_CLASS_NAME);

      clone.addClass(NG_ANIMATE_ANCHOR_CLASS_NAME);

      rootBodyElement.append(clone);

      var animatorIn, animatorOut = prepareOutAnimation();

      // the user may not end up using the `out` animation and
      // only making use of the `in` animation or vice-versa.
      // In either case we should allow this and not assume the
      // animation is over unless both animations are not used.
      if (!animatorOut) {
        animatorIn = prepareInAnimation();
        if (!animatorIn) {
          return end();
        }
      }

      var startingAnimator = animatorOut || animatorIn;

      return {
        start: function() {
          var runner;

          var currentAnimation = startingAnimator.start();
          currentAnimation.done(function() {
            currentAnimation = null;
            if (!animatorIn) {
              animatorIn = prepareInAnimation();
              if (animatorIn) {
                currentAnimation = animatorIn.start();
                currentAnimation.done(function() {
                  currentAnimation = null;
                  end();
                  runner.complete();
                });
                return currentAnimation;
              }
            }
            // in the event that there is no `in` animation
            end();
            runner.complete();
          });

          runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn
          });

          return runner;

          function endFn() {
            if (currentAnimation) {
              currentAnimation.end();
            }
          }
        }
      };

      function calculateAnchorStyles(anchor) {
        var styles = {};

        var coords = getDomNode(anchor).getBoundingClientRect();

        // we iterate directly since safari messes up and doesn't return
        // all the keys for the coords object when iterated
        forEach(['width','height','top','left'], function(key) {
          var value = coords[key];
          switch (key) {
            case 'top':
              value += bodyNode.scrollTop;
              break;
            case 'left':
              value += bodyNode.scrollLeft;
              break;
          }
          styles[key] = Math.floor(value) + 'px';
        });
        return styles;
      }

      function prepareOutAnimation() {
        var animator = $animateCss(clone, {
          addClass: NG_OUT_ANCHOR_CLASS_NAME,
          delay: true,
          from: calculateAnchorStyles(outAnchor)
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function getClassVal(element) {
        return element.attr('class') || '';
      }

      function prepareInAnimation() {
        var endingClasses = filterCssClasses(getClassVal(inAnchor));
        var toAdd = getUniqueValues(endingClasses, startingClasses);
        var toRemove = getUniqueValues(startingClasses, endingClasses);

        var animator = $animateCss(clone, {
          to: calculateAnchorStyles(inAnchor),
          addClass: NG_IN_ANCHOR_CLASS_NAME + ' ' + toAdd,
          removeClass: NG_OUT_ANCHOR_CLASS_NAME + ' ' + toRemove,
          delay: true
        });

        // read the comment within `prepareRegularAnimation` to understand
        // why this check is necessary
        return animator.$$willAnimate ? animator : null;
      }

      function end() {
        clone.remove();
        outAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
        inAnchor.removeClass(NG_ANIMATE_SHIM_CLASS_NAME);
      }
    }

    function prepareFromToAnchorAnimation(from, to, classes, anchors) {
      var fromAnimation = prepareRegularAnimation(from, noop);
      var toAnimation = prepareRegularAnimation(to, noop);

      var anchorAnimations = [];
      forEach(anchors, function(anchor) {
        var outElement = anchor['out'];
        var inElement = anchor['in'];
        var animator = prepareAnchoredAnimation(classes, outElement, inElement);
        if (animator) {
          anchorAnimations.push(animator);
        }
      });

      // no point in doing anything when there are no elements to animate
      if (!fromAnimation && !toAnimation && anchorAnimations.length === 0) return;

      return {
        start: function() {
          var animationRunners = [];

          if (fromAnimation) {
            animationRunners.push(fromAnimation.start());
          }

          if (toAnimation) {
            animationRunners.push(toAnimation.start());
          }

          forEach(anchorAnimations, function(animation) {
            animationRunners.push(animation.start());
          });

          var runner = new $$AnimateRunner({
            end: endFn,
            cancel: endFn // CSS-driven animations cannot be cancelled, only ended
          });

          $$AnimateRunner.all(animationRunners, function(status) {
            runner.complete(status);
          });

          return runner;

          function endFn() {
            forEach(animationRunners, function(runner) {
              runner.end();
            });
          }
        }
      };
    }

    function prepareRegularAnimation(animationDetails) {
      var element = animationDetails.element;
      var options = animationDetails.options || {};

      if (animationDetails.structural) {
        options.event = animationDetails.event;
        options.structural = true;
        options.applyClassesEarly = true;

        // we special case the leave animation since we want to ensure that
        // the element is removed as soon as the animation is over. Otherwise
        // a flicker might appear or the element may not be removed at all
        if (animationDetails.event === 'leave') {
          options.onDone = options.domOperation;
        }
      }

      // We assign the preparationClasses as the actual animation event since
      // the internals of $animateCss will just suffix the event token values
      // with `-active` to trigger the animation.
      if (options.preparationClasses) {
        options.event = concatWithSpace(options.event, options.preparationClasses);
      }

      var animator = $animateCss(element, options);

      // the driver lookup code inside of $$animation attempts to spawn a
      // driver one by one until a driver returns a.$$willAnimate animator object.
      // $animateCss will always return an object, however, it will pass in
      // a flag as a hint as to whether an animation was detected or not
      return animator.$$willAnimate ? animator : null;
    }
  }];
}];

// TODO(matsko): use caching here to speed things up for detection
// TODO(matsko): add documentation
//  by the time...

var $$AnimateJsProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  this.$get = ['$injector', '$$AnimateRunner', '$$jqLite',
       function($injector,   $$AnimateRunner,   $$jqLite) {

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);
         // $animateJs(element, 'enter');
    return function(element, event, classes, options) {
      var animationClosed = false;

      // the `classes` argument is optional and if it is not used
      // then the classes will be resolved from the element's className
      // property as well as options.addClass/options.removeClass.
      if (arguments.length === 3 && isObject(classes)) {
        options = classes;
        classes = null;
      }

      options = prepareAnimationOptions(options);
      if (!classes) {
        classes = element.attr('class') || '';
        if (options.addClass) {
          classes += ' ' + options.addClass;
        }
        if (options.removeClass) {
          classes += ' ' + options.removeClass;
        }
      }

      var classesToAdd = options.addClass;
      var classesToRemove = options.removeClass;

      // the lookupAnimations function returns a series of animation objects that are
      // matched up with one or more of the CSS classes. These animation objects are
      // defined via the module.animation factory function. If nothing is detected then
      // we don't return anything which then makes $animation query the next driver.
      var animations = lookupAnimations(classes);
      var before, after;
      if (animations.length) {
        var afterFn, beforeFn;
        if (event === 'leave') {
          beforeFn = 'leave';
          afterFn = 'afterLeave'; // TODO(matsko): get rid of this
        } else {
          beforeFn = 'before' + event.charAt(0).toUpperCase() + event.substr(1);
          afterFn = event;
        }

        if (event !== 'enter' && event !== 'move') {
          before = packageAnimations(element, event, options, animations, beforeFn);
        }
        after  = packageAnimations(element, event, options, animations, afterFn);
      }

      // no matching animations
      if (!before && !after) return;

      function applyOptions() {
        options.domOperation();
        applyAnimationClasses(element, options);
      }

      function close() {
        animationClosed = true;
        applyOptions();
        applyAnimationStyles(element, options);
      }

      var runner;

      return {
        $$willAnimate: true,
        end: function() {
          if (runner) {
            runner.end();
          } else {
            close();
            runner = new $$AnimateRunner();
            runner.complete(true);
          }
          return runner;
        },
        start: function() {
          if (runner) {
            return runner;
          }

          runner = new $$AnimateRunner();
          var closeActiveAnimations;
          var chain = [];

          if (before) {
            chain.push(function(fn) {
              closeActiveAnimations = before(fn);
            });
          }

          if (chain.length) {
            chain.push(function(fn) {
              applyOptions();
              fn(true);
            });
          } else {
            applyOptions();
          }

          if (after) {
            chain.push(function(fn) {
              closeActiveAnimations = after(fn);
            });
          }

          runner.setHost({
            end: function() {
              endAnimations();
            },
            cancel: function() {
              endAnimations(true);
            }
          });

          $$AnimateRunner.chain(chain, onComplete);
          return runner;

          function onComplete(success) {
            close(success);
            runner.complete(success);
          }

          function endAnimations(cancelled) {
            if (!animationClosed) {
              (closeActiveAnimations || noop)(cancelled);
              onComplete(cancelled);
            }
          }
        }
      };

      function executeAnimationFn(fn, element, event, options, onDone) {
        var args;
        switch (event) {
          case 'animate':
            args = [element, options.from, options.to, onDone];
            break;

          case 'setClass':
            args = [element, classesToAdd, classesToRemove, onDone];
            break;

          case 'addClass':
            args = [element, classesToAdd, onDone];
            break;

          case 'removeClass':
            args = [element, classesToRemove, onDone];
            break;

          default:
            args = [element, onDone];
            break;
        }

        args.push(options);

        var value = fn.apply(fn, args);
        if (value) {
          if (isFunction(value.start)) {
            value = value.start();
          }

          if (value instanceof $$AnimateRunner) {
            value.done(onDone);
          } else if (isFunction(value)) {
            // optional onEnd / onCancel callback
            return value;
          }
        }

        return noop;
      }

      function groupEventedAnimations(element, event, options, animations, fnName) {
        var operations = [];
        forEach(animations, function(ani) {
          var animation = ani[fnName];
          if (!animation) return;

          // note that all of these animations will run in parallel
          operations.push(function() {
            var runner;
            var endProgressCb;

            var resolved = false;
            var onAnimationComplete = function(rejected) {
              if (!resolved) {
                resolved = true;
                (endProgressCb || noop)(rejected);
                runner.complete(!rejected);
              }
            };

            runner = new $$AnimateRunner({
              end: function() {
                onAnimationComplete();
              },
              cancel: function() {
                onAnimationComplete(true);
              }
            });

            endProgressCb = executeAnimationFn(animation, element, event, options, function(result) {
              var cancelled = result === false;
              onAnimationComplete(cancelled);
            });

            return runner;
          });
        });

        return operations;
      }

      function packageAnimations(element, event, options, animations, fnName) {
        var operations = groupEventedAnimations(element, event, options, animations, fnName);
        if (operations.length === 0) {
          var a, b;
          if (fnName === 'beforeSetClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'beforeRemoveClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'beforeAddClass');
          } else if (fnName === 'setClass') {
            a = groupEventedAnimations(element, 'removeClass', options, animations, 'removeClass');
            b = groupEventedAnimations(element, 'addClass', options, animations, 'addClass');
          }

          if (a) {
            operations = operations.concat(a);
          }
          if (b) {
            operations = operations.concat(b);
          }
        }

        if (operations.length === 0) return;

        // TODO(matsko): add documentation
        return function startAnimation(callback) {
          var runners = [];
          if (operations.length) {
            forEach(operations, function(animateFn) {
              runners.push(animateFn());
            });
          }

          if (runners.length) {
            $$AnimateRunner.all(runners, callback);
          }  else {
            callback();
          }

          return function endFn(reject) {
            forEach(runners, function(runner) {
              if (reject) {
                runner.cancel();
              } else {
                runner.end();
              }
            });
          };
        };
      }
    };

    function lookupAnimations(classes) {
      classes = isArray(classes) ? classes : classes.split(' ');
      var matches = [], flagMap = {};
      for (var i = 0; i < classes.length; i++) {
        var klass = classes[i],
            animationFactory = $animateProvider.$$registeredAnimations[klass];
        if (animationFactory && !flagMap[klass]) {
          matches.push($injector.get(animationFactory));
          flagMap[klass] = true;
        }
      }
      return matches;
    }
  }];
}];

var $$AnimateJsDriverProvider = ['$$animationProvider', /** @this */ function($$animationProvider) {
  $$animationProvider.drivers.push('$$animateJsDriver');
  this.$get = ['$$animateJs', '$$AnimateRunner', function($$animateJs, $$AnimateRunner) {
    return function initDriverFn(animationDetails) {
      if (animationDetails.from && animationDetails.to) {
        var fromAnimation = prepareAnimation(animationDetails.from);
        var toAnimation = prepareAnimation(animationDetails.to);
        if (!fromAnimation && !toAnimation) return;

        return {
          start: function() {
            var animationRunners = [];

            if (fromAnimation) {
              animationRunners.push(fromAnimation.start());
            }

            if (toAnimation) {
              animationRunners.push(toAnimation.start());
            }

            $$AnimateRunner.all(animationRunners, done);

            var runner = new $$AnimateRunner({
              end: endFnFactory(),
              cancel: endFnFactory()
            });

            return runner;

            function endFnFactory() {
              return function() {
                forEach(animationRunners, function(runner) {
                  // at this point we cannot cancel animations for groups just yet. 1.5+
                  runner.end();
                });
              };
            }

            function done(status) {
              runner.complete(status);
            }
          }
        };
      } else {
        return prepareAnimation(animationDetails);
      }
    };

    function prepareAnimation(animationDetails) {
      // TODO(matsko): make sure to check for grouped animations and delegate down to normal animations
      var element = animationDetails.element;
      var event = animationDetails.event;
      var options = animationDetails.options;
      var classes = animationDetails.classes;
      return $$animateJs(element, event, classes, options);
    }
  }];
}];

var NG_ANIMATE_ATTR_NAME = 'data-ng-animate';
var NG_ANIMATE_PIN_DATA = '$ngAnimatePin';
var $$AnimateQueueProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var PRE_DIGEST_STATE = 1;
  var RUNNING_STATE = 2;
  var ONE_SPACE = ' ';

  var rules = this.rules = {
    skip: [],
    cancel: [],
    join: []
  };

  function getEventData(options) {
    return {
      addClass: options.addClass,
      removeClass: options.removeClass,
      from: options.from,
      to: options.to
    };
  }

  function makeTruthyCssClassMap(classString) {
    if (!classString) {
      return null;
    }

    var keys = classString.split(ONE_SPACE);
    var map = Object.create(null);

    forEach(keys, function(key) {
      map[key] = true;
    });
    return map;
  }

  function hasMatchingClasses(newClassString, currentClassString) {
    if (newClassString && currentClassString) {
      var currentClassMap = makeTruthyCssClassMap(currentClassString);
      return newClassString.split(ONE_SPACE).some(function(className) {
        return currentClassMap[className];
      });
    }
  }

  function isAllowed(ruleType, currentAnimation, previousAnimation) {
    return rules[ruleType].some(function(fn) {
      return fn(currentAnimation, previousAnimation);
    });
  }

  function hasAnimationClasses(animation, and) {
    var a = (animation.addClass || '').length > 0;
    var b = (animation.removeClass || '').length > 0;
    return and ? a && b : a || b;
  }

  rules.join.push(function(newAnimation, currentAnimation) {
    // if the new animation is class-based then we can just tack that on
    return !newAnimation.structural && hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // there is no need to animate anything if no classes are being added and
    // there is no structural animation that will be triggered
    return !newAnimation.structural && !hasAnimationClasses(newAnimation);
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // why should we trigger a new structural animation if the element will
    // be removed from the DOM anyway?
    return currentAnimation.event === 'leave' && newAnimation.structural;
  });

  rules.skip.push(function(newAnimation, currentAnimation) {
    // if there is an ongoing current animation then don't even bother running the class-based animation
    return currentAnimation.structural && currentAnimation.state === RUNNING_STATE && !newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // there can never be two structural animations running at the same time
    return currentAnimation.structural && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // if the previous animation is already running, but the new animation will
    // be triggered, but the new animation is structural
    return currentAnimation.state === RUNNING_STATE && newAnimation.structural;
  });

  rules.cancel.push(function(newAnimation, currentAnimation) {
    // cancel the animation if classes added / removed in both animation cancel each other out,
    // but only if the current animation isn't structural

    if (currentAnimation.structural) return false;

    var nA = newAnimation.addClass;
    var nR = newAnimation.removeClass;
    var cA = currentAnimation.addClass;
    var cR = currentAnimation.removeClass;

    // early detection to save the global CPU shortage :)
    if ((isUndefined(nA) && isUndefined(nR)) || (isUndefined(cA) && isUndefined(cR))) {
      return false;
    }

    return hasMatchingClasses(nA, cR) || hasMatchingClasses(nR, cA);
  });

  this.$get = ['$$rAF', '$rootScope', '$rootElement', '$document', '$$Map',
               '$$animation', '$$AnimateRunner', '$templateRequest', '$$jqLite', '$$forceReflow',
               '$$isDocumentHidden',
       function($$rAF,   $rootScope,   $rootElement,   $document,   $$Map,
                $$animation,   $$AnimateRunner,   $templateRequest,   $$jqLite,   $$forceReflow,
                $$isDocumentHidden) {

    var activeAnimationsLookup = new $$Map();
    var disabledElementsLookup = new $$Map();
    var animationsEnabled = null;

    function removeFromDisabledElementsLookup(evt) {
      disabledElementsLookup.delete(evt.target);
    }

    function postDigestTaskFactory() {
      var postDigestCalled = false;
      return function(fn) {
        // we only issue a call to postDigest before
        // it has first passed. This prevents any callbacks
        // from not firing once the animation has completed
        // since it will be out of the digest cycle.
        if (postDigestCalled) {
          fn();
        } else {
          $rootScope.$$postDigest(function() {
            postDigestCalled = true;
            fn();
          });
        }
      };
    }

    // Wait until all directive and route-related templates are downloaded and
    // compiled. The $templateRequest.totalPendingRequests variable keeps track of
    // all of the remote templates being currently downloaded. If there are no
    // templates currently downloading then the watcher will still fire anyway.
    var deregisterWatch = $rootScope.$watch(
      function() { return $templateRequest.totalPendingRequests === 0; },
      function(isEmpty) {
        if (!isEmpty) return;
        deregisterWatch();

        // Now that all templates have been downloaded, $animate will wait until
        // the post digest queue is empty before enabling animations. By having two
        // calls to $postDigest calls we can ensure that the flag is enabled at the
        // very end of the post digest queue. Since all of the animations in $animate
        // use $postDigest, it's important that the code below executes at the end.
        // This basically means that the page is fully downloaded and compiled before
        // any animations are triggered.
        $rootScope.$$postDigest(function() {
          $rootScope.$$postDigest(function() {
            // we check for null directly in the event that the application already called
            // .enabled() with whatever arguments that it provided it with
            if (animationsEnabled === null) {
              animationsEnabled = true;
            }
          });
        });
      }
    );

    var callbackRegistry = Object.create(null);

    // remember that the `customFilter`/`classNameFilter` are set during the
    // provider/config stage therefore we can optimize here and setup helper functions
    var customFilter = $animateProvider.customFilter();
    var classNameFilter = $animateProvider.classNameFilter();
    var returnTrue = function() { return true; };

    var isAnimatableByFilter = customFilter || returnTrue;
    var isAnimatableClassName = !classNameFilter ? returnTrue : function(node, options) {
      var className = [node.getAttribute('class'), options.addClass, options.removeClass].join(' ');
      return classNameFilter.test(className);
    };

    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function normalizeAnimationDetails(element, animation) {
      return mergeAnimationDetails(element, animation, {});
    }

    // IE9-11 has no method "contains" in SVG element and in Node.prototype. Bug #10259.
    var contains = window.Node.prototype.contains || /** @this */ function(arg) {
      // eslint-disable-next-line no-bitwise
      return this === arg || !!(this.compareDocumentPosition(arg) & 16);
    };

    function findCallbacks(targetParentNode, targetNode, event) {
      var matches = [];
      var entries = callbackRegistry[event];
      if (entries) {
        forEach(entries, function(entry) {
          if (contains.call(entry.node, targetNode)) {
            matches.push(entry.callback);
          } else if (event === 'leave' && contains.call(entry.node, targetParentNode)) {
            matches.push(entry.callback);
          }
        });
      }

      return matches;
    }

    function filterFromRegistry(list, matchContainer, matchCallback) {
      var containerNode = extractElementNode(matchContainer);
      return list.filter(function(entry) {
        var isMatch = entry.node === containerNode &&
                        (!matchCallback || entry.callback === matchCallback);
        return !isMatch;
      });
    }

    function cleanupEventListeners(phase, node) {
      if (phase === 'close' && !node.parentNode) {
        // If the element is not attached to a parentNode, it has been removed by
        // the domOperation, and we can safely remove the event callbacks
        $animate.off(node);
      }
    }

    var $animate = {
      on: function(event, container, callback) {
        var node = extractElementNode(container);
        callbackRegistry[event] = callbackRegistry[event] || [];
        callbackRegistry[event].push({
          node: node,
          callback: callback
        });

        // Remove the callback when the element is removed from the DOM
        jqLite(container).on('$destroy', function() {
          var animationDetails = activeAnimationsLookup.get(node);

          if (!animationDetails) {
            // If there's an animation ongoing, the callback calling code will remove
            // the event listeners. If we'd remove here, the callbacks would be removed
            // before the animation ends
            $animate.off(event, container, callback);
          }
        });
      },

      off: function(event, container, callback) {
        if (arguments.length === 1 && !isString(arguments[0])) {
          container = arguments[0];
          for (var eventType in callbackRegistry) {
            callbackRegistry[eventType] = filterFromRegistry(callbackRegistry[eventType], container);
          }

          return;
        }

        var entries = callbackRegistry[event];
        if (!entries) return;

        callbackRegistry[event] = arguments.length === 1
            ? null
            : filterFromRegistry(entries, container, callback);
      },

      pin: function(element, parentElement) {
        assertArg(isElement(element), 'element', 'not an element');
        assertArg(isElement(parentElement), 'parentElement', 'not an element');
        element.data(NG_ANIMATE_PIN_DATA, parentElement);
      },

      push: function(element, event, options, domOperation) {
        options = options || {};
        options.domOperation = domOperation;
        return queueAnimation(element, event, options);
      },

      // this method has four signatures:
      //  () - global getter
      //  (bool) - global setter
      //  (element) - element getter
      //  (element, bool) - element setter<F37>
      enabled: function(element, bool) {
        var argCount = arguments.length;

        if (argCount === 0) {
          // () - Global getter
          bool = !!animationsEnabled;
        } else {
          var hasElement = isElement(element);

          if (!hasElement) {
            // (bool) - Global setter
            bool = animationsEnabled = !!element;
          } else {
            var node = getDomNode(element);

            if (argCount === 1) {
              // (element) - Element getter
              bool = !disabledElementsLookup.get(node);
            } else {
              // (element, bool) - Element setter
              if (!disabledElementsLookup.has(node)) {
                // The element is added to the map for the first time.
                // Create a listener to remove it on `$destroy` (to avoid memory leak).
                jqLite(element).on('$destroy', removeFromDisabledElementsLookup);
              }
              disabledElementsLookup.set(node, !bool);
            }
          }
        }

        return bool;
      }
    };

    return $animate;

    function queueAnimation(originalElement, event, initialOptions) {
      // we always make a copy of the options since
      // there should never be any side effects on
      // the input data when running `$animateCss`.
      var options = copy(initialOptions);

      var element = stripCommentsFromElement(originalElement);
      var node = getDomNode(element);
      var parentNode = node && node.parentNode;

      options = prepareAnimationOptions(options);

      // we create a fake runner with a working promise.
      // These methods will become available after the digest has passed
      var runner = new $$AnimateRunner();

      // this is used to trigger callbacks in postDigest mode
      var runInNextPostDigestOrNow = postDigestTaskFactory();

      if (isArray(options.addClass)) {
        options.addClass = options.addClass.join(' ');
      }

      if (options.addClass && !isString(options.addClass)) {
        options.addClass = null;
      }

      if (isArray(options.removeClass)) {
        options.removeClass = options.removeClass.join(' ');
      }

      if (options.removeClass && !isString(options.removeClass)) {
        options.removeClass = null;
      }

      if (options.from && !isObject(options.from)) {
        options.from = null;
      }

      if (options.to && !isObject(options.to)) {
        options.to = null;
      }

      // If animations are hard-disabled for the whole application there is no need to continue.
      // There are also situations where a directive issues an animation for a jqLite wrapper that
      // contains only comment nodes. In this case, there is no way we can perform an animation.
      if (!animationsEnabled ||
          !node ||
          !isAnimatableByFilter(node, event, initialOptions) ||
          !isAnimatableClassName(node, options)) {
        close();
        return runner;
      }

      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      var documentHidden = $$isDocumentHidden();

      // This is a hard disable of all animations the element itself, therefore  there is no need to
      // continue further past this point if not enabled
      // Animations are also disabled if the document is currently hidden (page is not visible
      // to the user), because browsers slow down or do not flush calls to requestAnimationFrame
      var skipAnimations = documentHidden || disabledElementsLookup.get(node);
      var existingAnimation = (!skipAnimations && activeAnimationsLookup.get(node)) || {};
      var hasExistingAnimation = !!existingAnimation.state;

      // there is no point in traversing the same collection of parent ancestors if a followup
      // animation will be run on the same element that already did all that checking work
      if (!skipAnimations && (!hasExistingAnimation || existingAnimation.state !== PRE_DIGEST_STATE)) {
        skipAnimations = !areAnimationsAllowed(node, parentNode, event);
      }

      if (skipAnimations) {
        // Callbacks should fire even if the document is hidden (regression fix for issue #14120)
        if (documentHidden) notifyProgress(runner, event, 'start', getEventData(options));
        close();
        if (documentHidden) notifyProgress(runner, event, 'close', getEventData(options));
        return runner;
      }

      if (isStructural) {
        closeChildAnimations(node);
      }

      var newAnimation = {
        structural: isStructural,
        element: element,
        event: event,
        addClass: options.addClass,
        removeClass: options.removeClass,
        close: close,
        options: options,
        runner: runner
      };

      if (hasExistingAnimation) {
        var skipAnimationFlag = isAllowed('skip', newAnimation, existingAnimation);
        if (skipAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            close();
            return runner;
          } else {
            mergeAnimationDetails(element, existingAnimation, newAnimation);
            return existingAnimation.runner;
          }
        }
        var cancelAnimationFlag = isAllowed('cancel', newAnimation, existingAnimation);
        if (cancelAnimationFlag) {
          if (existingAnimation.state === RUNNING_STATE) {
            // this will end the animation right away and it is safe
            // to do so since the animation is already running and the
            // runner callback code will run in async
            existingAnimation.runner.end();
          } else if (existingAnimation.structural) {
            // this means that the animation is queued into a digest, but
            // hasn't started yet. Therefore it is safe to run the close
            // method which will call the runner methods in async.
            existingAnimation.close();
          } else {
            // this will merge the new animation options into existing animation options
            mergeAnimationDetails(element, existingAnimation, newAnimation);

            return existingAnimation.runner;
          }
        } else {
          // a joined animation means that this animation will take over the existing one
          // so an example would involve a leave animation taking over an enter. Then when
          // the postDigest kicks in the enter will be ignored.
          var joinAnimationFlag = isAllowed('join', newAnimation, existingAnimation);
          if (joinAnimationFlag) {
            if (existingAnimation.state === RUNNING_STATE) {
              normalizeAnimationDetails(element, newAnimation);
            } else {
              applyGeneratedPreparationClasses($$jqLite, element, isStructural ? event : null, options);

              event = newAnimation.event = existingAnimation.event;
              options = mergeAnimationDetails(element, existingAnimation, newAnimation);

              //we return the same runner since only the option values of this animation will
              //be fed into the `existingAnimation`.
              return existingAnimation.runner;
            }
          }
        }
      } else {
        // normalization in this case means that it removes redundant CSS classes that
        // already exist (addClass) or do not exist (removeClass) on the element
        normalizeAnimationDetails(element, newAnimation);
      }

      // when the options are merged and cleaned up we may end up not having to do
      // an animation at all, therefore we should check this before issuing a post
      // digest callback. Structural animations will always run no matter what.
      var isValidAnimation = newAnimation.structural;
      if (!isValidAnimation) {
        // animate (from/to) can be quickly checked first, otherwise we check if any classes are present
        isValidAnimation = (newAnimation.event === 'animate' && Object.keys(newAnimation.options.to || {}).length > 0)
                            || hasAnimationClasses(newAnimation);
      }

      if (!isValidAnimation) {
        close();
        clearElementAnimationState(node);
        return runner;
      }

      // the counter keeps track of cancelled animations
      var counter = (existingAnimation.counter || 0) + 1;
      newAnimation.counter = counter;

      markElementAnimationState(node, PRE_DIGEST_STATE, newAnimation);

      $rootScope.$$postDigest(function() {
        // It is possible that the DOM nodes inside `originalElement` have been replaced. This can
        // happen if the animated element is a transcluded clone and also has a `templateUrl`
        // directive on it. Therefore, we must recreate `element` in order to interact with the
        // actual DOM nodes.
        // Note: We still need to use the old `node` for certain things, such as looking up in
        //       HashMaps where it was used as the key.

        element = stripCommentsFromElement(originalElement);

        var animationDetails = activeAnimationsLookup.get(node);
        var animationCancelled = !animationDetails;
        animationDetails = animationDetails || {};

        // if addClass/removeClass is called before something like enter then the
        // registered parent element may not be present. The code below will ensure
        // that a final value for parent element is obtained
        var parentElement = element.parent() || [];

        // animate/structural/class-based animations all have requirements. Otherwise there
        // is no point in performing an animation. The parent node must also be set.
        var isValidAnimation = parentElement.length > 0
                                && (animationDetails.event === 'animate'
                                    || animationDetails.structural
                                    || hasAnimationClasses(animationDetails));

        // this means that the previous animation was cancelled
        // even if the follow-up animation is the same event
        if (animationCancelled || animationDetails.counter !== counter || !isValidAnimation) {
          // if another animation did not take over then we need
          // to make sure that the domOperation and options are
          // handled accordingly
          if (animationCancelled) {
            applyAnimationClasses(element, options);
            applyAnimationStyles(element, options);
          }

          // if the event changed from something like enter to leave then we do
          // it, otherwise if it's the same then the end result will be the same too
          if (animationCancelled || (isStructural && animationDetails.event !== event)) {
            options.domOperation();
            runner.end();
          }

          // in the event that the element animation was not cancelled or a follow-up animation
          // isn't allowed to animate from here then we need to clear the state of the element
          // so that any future animations won't read the expired animation data.
          if (!isValidAnimation) {
            clearElementAnimationState(node);
          }

          return;
        }

        // this combined multiple class to addClass / removeClass into a setClass event
        // so long as a structural event did not take over the animation
        event = !animationDetails.structural && hasAnimationClasses(animationDetails, true)
            ? 'setClass'
            : animationDetails.event;

        markElementAnimationState(node, RUNNING_STATE);
        var realRunner = $$animation(element, event, animationDetails.options);

        // this will update the runner's flow-control events based on
        // the `realRunner` object.
        runner.setHost(realRunner);
        notifyProgress(runner, event, 'start', getEventData(options));

        realRunner.done(function(status) {
          close(!status);
          var animationDetails = activeAnimationsLookup.get(node);
          if (animationDetails && animationDetails.counter === counter) {
            clearElementAnimationState(node);
          }
          notifyProgress(runner, event, 'close', getEventData(options));
        });
      });

      return runner;

      function notifyProgress(runner, event, phase, data) {
        runInNextPostDigestOrNow(function() {
          var callbacks = findCallbacks(parentNode, node, event);
          if (callbacks.length) {
            // do not optimize this call here to RAF because
            // we don't know how heavy the callback code here will
            // be and if this code is buffered then this can
            // lead to a performance regression.
            $$rAF(function() {
              forEach(callbacks, function(callback) {
                callback(element, phase, data);
              });
              cleanupEventListeners(phase, node);
            });
          } else {
            cleanupEventListeners(phase, node);
          }
        });
        runner.progress(event, phase, data);
      }

      function close(reject) {
        clearGeneratedClasses(element, options);
        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();
        runner.complete(!reject);
      }
    }

    function closeChildAnimations(node) {
      var children = node.querySelectorAll('[' + NG_ANIMATE_ATTR_NAME + ']');
      forEach(children, function(child) {
        var state = parseInt(child.getAttribute(NG_ANIMATE_ATTR_NAME), 10);
        var animationDetails = activeAnimationsLookup.get(child);
        if (animationDetails) {
          switch (state) {
            case RUNNING_STATE:
              animationDetails.runner.end();
              /* falls through */
            case PRE_DIGEST_STATE:
              activeAnimationsLookup.delete(child);
              break;
          }
        }
      });
    }

    function clearElementAnimationState(node) {
      node.removeAttribute(NG_ANIMATE_ATTR_NAME);
      activeAnimationsLookup.delete(node);
    }

    /**
     * This fn returns false if any of the following is true:
     * a) animations on any parent element are disabled, and animations on the element aren't explicitly allowed
     * b) a parent element has an ongoing structural animation, and animateChildren is false
     * c) the element is not a child of the body
     * d) the element is not a child of the $rootElement
     */
    function areAnimationsAllowed(node, parentNode, event) {
      var bodyNode = $document[0].body;
      var rootNode = getDomNode($rootElement);

      var bodyNodeDetected = (node === bodyNode) || node.nodeName === 'HTML';
      var rootNodeDetected = (node === rootNode);
      var parentAnimationDetected = false;
      var elementDisabled = disabledElementsLookup.get(node);
      var animateChildren;

      var parentHost = jqLite.data(node, NG_ANIMATE_PIN_DATA);
      if (parentHost) {
        parentNode = getDomNode(parentHost);
      }

      while (parentNode) {
        if (!rootNodeDetected) {
          // AngularJS doesn't want to attempt to animate elements outside of the application
          // therefore we need to ensure that the rootElement is an ancestor of the current element
          rootNodeDetected = (parentNode === rootNode);
        }

        if (parentNode.nodeType !== ELEMENT_NODE) {
          // no point in inspecting the #document element
          break;
        }

        var details = activeAnimationsLookup.get(parentNode) || {};
        // either an enter, leave or move animation will commence
        // therefore we can't allow any animations to take place
        // but if a parent animation is class-based then that's ok
        if (!parentAnimationDetected) {
          var parentNodeDisabled = disabledElementsLookup.get(parentNode);

          if (parentNodeDisabled === true && elementDisabled !== false) {
            // disable animations if the user hasn't explicitly enabled animations on the
            // current element
            elementDisabled = true;
            // element is disabled via parent element, no need to check anything else
            break;
          } else if (parentNodeDisabled === false) {
            elementDisabled = false;
          }
          parentAnimationDetected = details.structural;
        }

        if (isUndefined(animateChildren) || animateChildren === true) {
          var value = jqLite.data(parentNode, NG_ANIMATE_CHILDREN_DATA);
          if (isDefined(value)) {
            animateChildren = value;
          }
        }

        // there is no need to continue traversing at this point
        if (parentAnimationDetected && animateChildren === false) break;

        if (!bodyNodeDetected) {
          // we also need to ensure that the element is or will be a part of the body element
          // otherwise it is pointless to even issue an animation to be rendered
          bodyNodeDetected = (parentNode === bodyNode);
        }

        if (bodyNodeDetected && rootNodeDetected) {
          // If both body and root have been found, any other checks are pointless,
          // as no animation data should live outside the application
          break;
        }

        if (!rootNodeDetected) {
          // If `rootNode` is not detected, check if `parentNode` is pinned to another element
          parentHost = jqLite.data(parentNode, NG_ANIMATE_PIN_DATA);
          if (parentHost) {
            // The pin target element becomes the next parent element
            parentNode = getDomNode(parentHost);
            continue;
          }
        }

        parentNode = parentNode.parentNode;
      }

      var allowAnimation = (!parentAnimationDetected || animateChildren) && elementDisabled !== true;
      return allowAnimation && rootNodeDetected && bodyNodeDetected;
    }

    function markElementAnimationState(node, state, details) {
      details = details || {};
      details.state = state;

      node.setAttribute(NG_ANIMATE_ATTR_NAME, state);

      var oldValue = activeAnimationsLookup.get(node);
      var newValue = oldValue
          ? extend(oldValue, details)
          : details;
      activeAnimationsLookup.set(node, newValue);
    }
  }];
}];

/** @this */
var $$AnimateCacheProvider = function() {

  var KEY = '$$ngAnimateParentKey';
  var parentCounter = 0;
  var cache = Object.create(null);

  this.$get = [function() {
    return {
      cacheKey: function(node, method, addClass, removeClass) {
        var parentNode = node.parentNode;
        var parentID = parentNode[KEY] || (parentNode[KEY] = ++parentCounter);
        var parts = [parentID, method, node.getAttribute('class')];
        if (addClass) {
          parts.push(addClass);
        }
        if (removeClass) {
          parts.push(removeClass);
        }
        return parts.join(' ');
      },

      containsCachedAnimationWithoutDuration: function(key) {
        var entry = cache[key];

        // nothing cached, so go ahead and animate
        // otherwise it should be a valid animation
        return (entry && !entry.isValid) || false;
      },

      flush: function() {
        cache = Object.create(null);
      },

      count: function(key) {
        var entry = cache[key];
        return entry ? entry.total : 0;
      },

      get: function(key) {
        var entry = cache[key];
        return entry && entry.value;
      },

      put: function(key, value, isValid) {
        if (!cache[key]) {
          cache[key] = { total: 1, value: value, isValid: isValid };
        } else {
          cache[key].total++;
          cache[key].value = value;
        }
      }
    };
  }];
};

/* exported $$AnimationProvider */

var $$AnimationProvider = ['$animateProvider', /** @this */ function($animateProvider) {
  var NG_ANIMATE_REF_ATTR = 'ng-animate-ref';

  var drivers = this.drivers = [];

  var RUNNER_STORAGE_KEY = '$$animationRunner';
  var PREPARE_CLASSES_KEY = '$$animatePrepareClasses';

  function setRunner(element, runner) {
    element.data(RUNNER_STORAGE_KEY, runner);
  }

  function removeRunner(element) {
    element.removeData(RUNNER_STORAGE_KEY);
  }

  function getRunner(element) {
    return element.data(RUNNER_STORAGE_KEY);
  }

  this.$get = ['$$jqLite', '$rootScope', '$injector', '$$AnimateRunner', '$$Map', '$$rAFScheduler', '$$animateCache',
       function($$jqLite,   $rootScope,   $injector,   $$AnimateRunner,   $$Map,   $$rAFScheduler, $$animateCache) {

    var animationQueue = [];
    var applyAnimationClasses = applyAnimationClassesFactory($$jqLite);

    function sortAnimations(animations) {
      var tree = { children: [] };
      var i, lookup = new $$Map();

      // this is done first beforehand so that the map
      // is filled with a list of the elements that will be animated
      for (i = 0; i < animations.length; i++) {
        var animation = animations[i];
        lookup.set(animation.domNode, animations[i] = {
          domNode: animation.domNode,
          element: animation.element,
          fn: animation.fn,
          children: []
        });
      }

      for (i = 0; i < animations.length; i++) {
        processNode(animations[i]);
      }

      return flatten(tree);

      function processNode(entry) {
        if (entry.processed) return entry;
        entry.processed = true;

        var elementNode = entry.domNode;
        var parentNode = elementNode.parentNode;
        lookup.set(elementNode, entry);

        var parentEntry;
        while (parentNode) {
          parentEntry = lookup.get(parentNode);
          if (parentEntry) {
            if (!parentEntry.processed) {
              parentEntry = processNode(parentEntry);
            }
            break;
          }
          parentNode = parentNode.parentNode;
        }

        (parentEntry || tree).children.push(entry);
        return entry;
      }

      function flatten(tree) {
        var result = [];
        var queue = [];
        var i;

        for (i = 0; i < tree.children.length; i++) {
          queue.push(tree.children[i]);
        }

        var remainingLevelEntries = queue.length;
        var nextLevelEntries = 0;
        var row = [];

        for (i = 0; i < queue.length; i++) {
          var entry = queue[i];
          if (remainingLevelEntries <= 0) {
            remainingLevelEntries = nextLevelEntries;
            nextLevelEntries = 0;
            result.push(row);
            row = [];
          }
          row.push(entry);
          entry.children.forEach(function(childEntry) {
            nextLevelEntries++;
            queue.push(childEntry);
          });
          remainingLevelEntries--;
        }

        if (row.length) {
          result.push(row);
        }

        return result;
      }
    }

    // TODO(matsko): document the signature in a better way
    return function(element, event, options) {
      options = prepareAnimationOptions(options);
      var isStructural = ['enter', 'move', 'leave'].indexOf(event) >= 0;

      // there is no animation at the current moment, however
      // these runner methods will get later updated with the
      // methods leading into the driver's end/cancel methods
      // for now they just stop the animation from starting
      var runner = new $$AnimateRunner({
        end: function() { close(); },
        cancel: function() { close(true); }
      });

      if (!drivers.length) {
        close();
        return runner;
      }

      var classes = mergeClasses(element.attr('class'), mergeClasses(options.addClass, options.removeClass));
      var tempClasses = options.tempClasses;
      if (tempClasses) {
        classes += ' ' + tempClasses;
        options.tempClasses = null;
      }

      if (isStructural) {
        element.data(PREPARE_CLASSES_KEY, 'ng-' + event + PREPARE_CLASS_SUFFIX);
      }

      setRunner(element, runner);

      animationQueue.push({
        // this data is used by the postDigest code and passed into
        // the driver step function
        element: element,
        classes: classes,
        event: event,
        structural: isStructural,
        options: options,
        beforeStart: beforeStart,
        close: close
      });

      element.on('$destroy', handleDestroyedElement);

      // we only want there to be one function called within the post digest
      // block. This way we can group animations for all the animations that
      // were apart of the same postDigest flush call.
      if (animationQueue.length > 1) return runner;

      $rootScope.$$postDigest(function() {
        var animations = [];
        forEach(animationQueue, function(entry) {
          // the element was destroyed early on which removed the runner
          // form its storage. This means we can't animate this element
          // at all and it already has been closed due to destruction.
          if (getRunner(entry.element)) {
            animations.push(entry);
          } else {
            entry.close();
          }
        });

        // now any future animations will be in another postDigest
        animationQueue.length = 0;

        var groupedAnimations = groupAnimations(animations);
        var toBeSortedAnimations = [];

        forEach(groupedAnimations, function(animationEntry) {
          var element = animationEntry.from ? animationEntry.from.element : animationEntry.element;
          var extraClasses = options.addClass;

          extraClasses = (extraClasses ? (extraClasses + ' ') : '') + NG_ANIMATE_CLASSNAME;
          var cacheKey = $$animateCache.cacheKey(element[0], animationEntry.event, extraClasses, options.removeClass);

          toBeSortedAnimations.push({
            element: element,
            domNode: getDomNode(element),
            fn: function triggerAnimationStart() {
              var startAnimationFn, closeFn = animationEntry.close;

              // in the event that we've cached the animation status for this element
              // and it's in fact an invalid animation (something that has duration = 0)
              // then we should skip all the heavy work from here on
              if ($$animateCache.containsCachedAnimationWithoutDuration(cacheKey)) {
                closeFn();
                return;
              }

              // it's important that we apply the `ng-animate` CSS class and the
              // temporary classes before we do any driver invoking since these
              // CSS classes may be required for proper CSS detection.
              animationEntry.beforeStart();

              // in the event that the element was removed before the digest runs or
              // during the RAF sequencing then we should not trigger the animation.
              var targetElement = animationEntry.anchors
                  ? (animationEntry.from.element || animationEntry.to.element)
                  : animationEntry.element;

              if (getRunner(targetElement)) {
                var operation = invokeFirstDriver(animationEntry);
                if (operation) {
                  startAnimationFn = operation.start;
                }
              }

              if (!startAnimationFn) {
                closeFn();
              } else {
                var animationRunner = startAnimationFn();
                animationRunner.done(function(status) {
                  closeFn(!status);
                });
                updateAnimationRunners(animationEntry, animationRunner);
              }
            }
          });
        });

        // we need to sort each of the animations in order of parent to child
        // relationships. This ensures that the child classes are applied at the
        // right time.
        var finalAnimations = sortAnimations(toBeSortedAnimations);
        for (var i = 0; i < finalAnimations.length; i++) {
          var innerArray = finalAnimations[i];
          for (var j = 0; j < innerArray.length; j++) {
            var entry = innerArray[j];
            var element = entry.element;

            // the RAFScheduler code only uses functions
            finalAnimations[i][j] = entry.fn;

            // the first row of elements shouldn't have a prepare-class added to them
            // since the elements are at the top of the animation hierarchy and they
            // will be applied without a RAF having to pass...
            if (i === 0) {
              element.removeData(PREPARE_CLASSES_KEY);
              continue;
            }

            var prepareClassName = element.data(PREPARE_CLASSES_KEY);
            if (prepareClassName) {
              $$jqLite.addClass(element, prepareClassName);
            }
          }
        }

        $$rAFScheduler(finalAnimations);
      });

      return runner;

      // TODO(matsko): change to reference nodes
      function getAnchorNodes(node) {
        var SELECTOR = '[' + NG_ANIMATE_REF_ATTR + ']';
        var items = node.hasAttribute(NG_ANIMATE_REF_ATTR)
              ? [node]
              : node.querySelectorAll(SELECTOR);
        var anchors = [];
        forEach(items, function(node) {
          var attr = node.getAttribute(NG_ANIMATE_REF_ATTR);
          if (attr && attr.length) {
            anchors.push(node);
          }
        });
        return anchors;
      }

      function groupAnimations(animations) {
        var preparedAnimations = [];
        var refLookup = {};
        forEach(animations, function(animation, index) {
          var element = animation.element;
          var node = getDomNode(element);
          var event = animation.event;
          var enterOrMove = ['enter', 'move'].indexOf(event) >= 0;
          var anchorNodes = animation.structural ? getAnchorNodes(node) : [];

          if (anchorNodes.length) {
            var direction = enterOrMove ? 'to' : 'from';

            forEach(anchorNodes, function(anchor) {
              var key = anchor.getAttribute(NG_ANIMATE_REF_ATTR);
              refLookup[key] = refLookup[key] || {};
              refLookup[key][direction] = {
                animationID: index,
                element: jqLite(anchor)
              };
            });
          } else {
            preparedAnimations.push(animation);
          }
        });

        var usedIndicesLookup = {};
        var anchorGroups = {};
        forEach(refLookup, function(operations, key) {
          var from = operations.from;
          var to = operations.to;

          if (!from || !to) {
            // only one of these is set therefore we can't have an
            // anchor animation since all three pieces are required
            var index = from ? from.animationID : to.animationID;
            var indexKey = index.toString();
            if (!usedIndicesLookup[indexKey]) {
              usedIndicesLookup[indexKey] = true;
              preparedAnimations.push(animations[index]);
            }
            return;
          }

          var fromAnimation = animations[from.animationID];
          var toAnimation = animations[to.animationID];
          var lookupKey = from.animationID.toString();
          if (!anchorGroups[lookupKey]) {
            var group = anchorGroups[lookupKey] = {
              structural: true,
              beforeStart: function() {
                fromAnimation.beforeStart();
                toAnimation.beforeStart();
              },
              close: function() {
                fromAnimation.close();
                toAnimation.close();
              },
              classes: cssClassesIntersection(fromAnimation.classes, toAnimation.classes),
              from: fromAnimation,
              to: toAnimation,
              anchors: [] // TODO(matsko): change to reference nodes
            };

            // the anchor animations require that the from and to elements both have at least
            // one shared CSS class which effectively marries the two elements together to use
            // the same animation driver and to properly sequence the anchor animation.
            if (group.classes.length) {
              preparedAnimations.push(group);
            } else {
              preparedAnimations.push(fromAnimation);
              preparedAnimations.push(toAnimation);
            }
          }

          anchorGroups[lookupKey].anchors.push({
            'out': from.element, 'in': to.element
          });
        });

        return preparedAnimations;
      }

      function cssClassesIntersection(a,b) {
        a = a.split(' ');
        b = b.split(' ');
        var matches = [];

        for (var i = 0; i < a.length; i++) {
          var aa = a[i];
          if (aa.substring(0,3) === 'ng-') continue;

          for (var j = 0; j < b.length; j++) {
            if (aa === b[j]) {
              matches.push(aa);
              break;
            }
          }
        }

        return matches.join(' ');
      }

      function invokeFirstDriver(animationDetails) {
        // we loop in reverse order since the more general drivers (like CSS and JS)
        // may attempt more elements, but custom drivers are more particular
        for (var i = drivers.length - 1; i >= 0; i--) {
          var driverName = drivers[i];
          var factory = $injector.get(driverName);
          var driver = factory(animationDetails);
          if (driver) {
            return driver;
          }
        }
      }

      function beforeStart() {
        tempClasses = (tempClasses ? (tempClasses + ' ') : '') + NG_ANIMATE_CLASSNAME;
        $$jqLite.addClass(element, tempClasses);

        var prepareClassName = element.data(PREPARE_CLASSES_KEY);
        if (prepareClassName) {
          $$jqLite.removeClass(element, prepareClassName);
          prepareClassName = null;
        }
      }

      function updateAnimationRunners(animation, newRunner) {
        if (animation.from && animation.to) {
          update(animation.from.element);
          update(animation.to.element);
        } else {
          update(animation.element);
        }

        function update(element) {
          var runner = getRunner(element);
          if (runner) runner.setHost(newRunner);
        }
      }

      function handleDestroyedElement() {
        var runner = getRunner(element);
        if (runner && (event !== 'leave' || !options.$$domOperationFired)) {
          runner.end();
        }
      }

      function close(rejected) {
        element.off('$destroy', handleDestroyedElement);
        removeRunner(element);

        applyAnimationClasses(element, options);
        applyAnimationStyles(element, options);
        options.domOperation();

        if (tempClasses) {
          $$jqLite.removeClass(element, tempClasses);
        }

        runner.complete(!rejected);
      }
    };
  }];
}];

/**
 * @ngdoc directive
 * @name ngAnimateSwap
 * @restrict A
 * @scope
 *
 * @description
 *
 * ngAnimateSwap is a animation-oriented directive that allows for the container to
 * be removed and entered in whenever the associated expression changes. A
 * common usecase for this directive is a rotating banner or slider component which
 * contains one image being present at a time. When the active image changes
 * then the old image will perform a `leave` animation and the new element
 * will be inserted via an `enter` animation.
 *
 * @animations
 * | Animation                        | Occurs                               |
 * |----------------------------------|--------------------------------------|
 * | {@link ng.$animate#enter enter}  | when the new element is inserted to the DOM  |
 * | {@link ng.$animate#leave leave}  | when the old element is removed from the DOM |
 *
 * @example
 * <example name="ngAnimateSwap-directive" module="ngAnimateSwapExample"
 *          deps="angular-animate.js"
 *          animations="true" fixBase="true">
 *   <file name="index.html">
 *     <div class="container" ng-controller="AppCtrl">
 *       <div ng-animate-swap="number" class="cell swap-animation" ng-class="colorClass(number)">
 *         {{ number }}
 *       </div>
 *     </div>
 *   </file>
 *   <file name="script.js">
 *     angular.module('ngAnimateSwapExample', ['ngAnimate'])
 *       .controller('AppCtrl', ['$scope', '$interval', function($scope, $interval) {
 *         $scope.number = 0;
 *         $interval(function() {
 *           $scope.number++;
 *         }, 1000);
 *
 *         var colors = ['red','blue','green','yellow','orange'];
 *         $scope.colorClass = function(number) {
 *           return colors[number % colors.length];
 *         };
 *       }]);
 *   </file>
 *  <file name="animations.css">
 *  .container {
 *    height:250px;
 *    width:250px;
 *    position:relative;
 *    overflow:hidden;
 *    border:2px solid black;
 *  }
 *  .container .cell {
 *    font-size:150px;
 *    text-align:center;
 *    line-height:250px;
 *    position:absolute;
 *    top:0;
 *    left:0;
 *    right:0;
 *    border-bottom:2px solid black;
 *  }
 *  .swap-animation.ng-enter, .swap-animation.ng-leave {
 *    transition:0.5s linear all;
 *  }
 *  .swap-animation.ng-enter {
 *    top:-250px;
 *  }
 *  .swap-animation.ng-enter-active {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave {
 *    top:0px;
 *  }
 *  .swap-animation.ng-leave-active {
 *    top:250px;
 *  }
 *  .red { background:red; }
 *  .green { background:green; }
 *  .blue { background:blue; }
 *  .yellow { background:yellow; }
 *  .orange { background:orange; }
 *  </file>
 * </example>
 */
var ngAnimateSwapDirective = ['$animate', function($animate) {
  return {
    restrict: 'A',
    transclude: 'element',
    terminal: true,
    priority: 550, // We use 550 here to ensure that the directive is caught before others,
                   // but after `ngIf` (at priority 600).
    link: function(scope, $element, attrs, ctrl, $transclude) {
      var previousElement, previousScope;
      scope.$watchCollection(attrs.ngAnimateSwap || attrs['for'], function(value) {
        if (previousElement) {
          $animate.leave(previousElement);
        }
        if (previousScope) {
          previousScope.$destroy();
          previousScope = null;
        }
        if (value || value === 0) {
          $transclude(function(clone, childScope) {
            previousElement = clone;
            previousScope = childScope;
            $animate.enter(clone, null, $element);
          });
        }
      });
    }
  };
}];

/**
 * @ngdoc module
 * @name ngAnimate
 * @description
 *
 * The `ngAnimate` module provides support for CSS-based animations (keyframes and transitions) as well as JavaScript-based animations via
 * callback hooks. Animations are not enabled by default, however, by including `ngAnimate` the animation hooks are enabled for an AngularJS app.
 *
 * ## Usage
 * Simply put, there are two ways to make use of animations when ngAnimate is used: by using **CSS** and **JavaScript**. The former works purely based
 * using CSS (by using matching CSS selectors/styles) and the latter triggers animations that are registered via `module.animation()`. For
 * both CSS and JS animations the sole requirement is to have a matching `CSS class` that exists both in the registered animation and within
 * the HTML element that the animation will be triggered on.
 *
 * ## Directive Support
 * The following directives are "animation aware":
 *
 * | Directive                                                                     | Supported Animations                                                      |
 * |-------------------------------------------------------------------------------|---------------------------------------------------------------------------|
 * | {@link ng.directive:form#animations form / ngForm}                            | add and remove ({@link ng.directive:form#css-classes various classes})    |
 * | {@link ngAnimate.directive:ngAnimateSwap#animations ngAnimateSwap}            | enter and leave                                                           |
 * | {@link ng.directive:ngClass#animations ngClass / {{class&#125;&#8203;&#125;}  | add and remove                                                            |
 * | {@link ng.directive:ngClassEven#animations ngClassEven}                       | add and remove                                                            |
 * | {@link ng.directive:ngClassOdd#animations ngClassOdd}                         | add and remove                                                            |
 * | {@link ng.directive:ngHide#animations ngHide}                                 | add and remove (the `ng-hide` class)                                      |
 * | {@link ng.directive:ngIf#animations ngIf}                                     | enter and leave                                                           |
 * | {@link ng.directive:ngInclude#animations ngInclude}                           | enter and leave                                                           |
 * | {@link module:ngMessages#animations ngMessage / ngMessageExp}                 | enter and leave                                                           |
 * | {@link module:ngMessages#animations ngMessages}                               | add and remove (the `ng-active`/`ng-inactive` classes)                    |
 * | {@link ng.directive:ngModel#animations ngModel}                               | add and remove ({@link ng.directive:ngModel#css-classes various classes}) |
 * | {@link ng.directive:ngRepeat#animations ngRepeat}                             | enter, leave, and move                                                    |
 * | {@link ng.directive:ngShow#animations ngShow}                                 | add and remove (the `ng-hide` class)                                      |
 * | {@link ng.directive:ngSwitch#animations ngSwitch}                             | enter and leave                                                           |
 * | {@link ngRoute.directive:ngView#animations ngView}                            | enter and leave                                                           |
 *
 * (More information can be found by visiting the documentation associated with each directive.)
 *
 * For a full breakdown of the steps involved during each animation event, refer to the
 * {@link ng.$animate `$animate` API docs}.
 *
 * ## CSS-based Animations
 *
 * CSS-based animations with ngAnimate are unique since they require no JavaScript code at all. By using a CSS class that we reference between our HTML
 * and CSS code we can create an animation that will be picked up by AngularJS when an underlying directive performs an operation.
 *
 * The example below shows how an `enter` animation can be made possible on an element using `ng-if`:
 *
 * ```html
 * <div ng-if="bool" class="fade">
 *    Fade me in out
 * </div>
 * <button ng-click="bool=true">Fade In!</button>
 * <button ng-click="bool=false">Fade Out!</button>
 * ```
 *
 * Notice the CSS class **fade**? We can now create the CSS transition code that references this class:
 *
 * ```css
 * /&#42; The starting CSS styles for the enter animation &#42;/
 * .fade.ng-enter {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 *
 * /&#42; The finishing CSS styles for the enter animation &#42;/
 * .fade.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * ```
 *
 * The key thing to remember here is that, depending on the animation event (which each of the directives above trigger depending on what's going on) two
 * generated CSS classes will be applied to the element; in the example above we have `.ng-enter` and `.ng-enter-active`. For CSS transitions, the transition
 * code **must** be defined within the starting CSS class (in this case `.ng-enter`). The destination class is what the transition will animate towards.
 *
 * If for example we wanted to create animations for `leave` and `move` (ngRepeat triggers move) then we can do so using the same CSS naming conventions:
 *
 * ```css
 * /&#42; now the element will fade out before it is removed from the DOM &#42;/
 * .fade.ng-leave {
 *   transition:0.5s linear all;
 *   opacity:1;
 * }
 * .fade.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * We can also make use of **CSS Keyframes** by referencing the keyframe animation within the starting CSS class:
 *
 * ```css
 * /&#42; there is no need to define anything inside of the destination
 * CSS class since the keyframe will take charge of the animation &#42;/
 * .fade.ng-leave {
 *   animation: my_fade_animation 0.5s linear;
 *   -webkit-animation: my_fade_animation 0.5s linear;
 * }
 *
 * @keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 *
 * @-webkit-keyframes my_fade_animation {
 *   from { opacity:1; }
 *   to { opacity:0; }
 * }
 * ```
 *
 * Feel free also mix transitions and keyframes together as well as any other CSS classes on the same element.
 *
 * ### CSS Class-based Animations
 *
 * Class-based animations (animations that are triggered via `ngClass`, `ngShow`, `ngHide` and some other directives) have a slightly different
 * naming convention. Class-based animations are basic enough that a standard transition or keyframe can be referenced on the class being added
 * and removed.
 *
 * For example if we wanted to do a CSS animation for `ngHide` then we place an animation on the `.ng-hide` CSS class:
 *
 * ```html
 * <div ng-show="bool" class="fade">
 *   Show and hide me
 * </div>
 * <button ng-click="bool=!bool">Toggle</button>
 *
 * <style>
 * .fade.ng-hide {
 *   transition:0.5s linear all;
 *   opacity:0;
 * }
 * </style>
 * ```
 *
 * All that is going on here with ngShow/ngHide behind the scenes is the `.ng-hide` class is added/removed (when the hidden state is valid). Since
 * ngShow and ngHide are animation aware then we can match up a transition and ngAnimate handles the rest.
 *
 * In addition the addition and removal of the CSS class, ngAnimate also provides two helper methods that we can use to further decorate the animation
 * with CSS styles.
 *
 * ```html
 * <div ng-class="{on:onOff}" class="highlight">
 *   Highlight this box
 * </div>
 * <button ng-click="onOff=!onOff">Toggle</button>
 *
 * <style>
 * .highlight {
 *   transition:0.5s linear all;
 * }
 * .highlight.on-add {
 *   background:white;
 * }
 * .highlight.on {
 *   background:yellow;
 * }
 * .highlight.on-remove {
 *   background:black;
 * }
 * </style>
 * ```
 *
 * We can also make use of CSS keyframes by placing them within the CSS classes.
 *
 *
 * ### CSS Staggering Animations
 * A Staggering animation is a collection of animations that are issued with a slight delay in between each successive operation resulting in a
 * curtain-like effect. The ngAnimate module (versions >=1.2) supports staggering animations and the stagger effect can be
 * performed by creating a **ng-EVENT-stagger** CSS class and attaching that class to the base CSS class used for
 * the animation. The style property expected within the stagger class can either be a **transition-delay** or an
 * **animation-delay** property (or both if your animation contains both transitions and keyframe animations).
 *
 * ```css
 * .my-animation.ng-enter {
 *   /&#42; standard transition code &#42;/
 *   transition: 1s linear all;
 *   opacity:0;
 * }
 * .my-animation.ng-enter-stagger {
 *   /&#42; this will have a 100ms delay between each successive leave animation &#42;/
 *   transition-delay: 0.1s;
 *
 *   /&#42; As of 1.4.4, this must always be set: it signals ngAnimate
 *     to not accidentally inherit a delay property from another CSS class &#42;/
 *   transition-duration: 0s;
 *
 *   /&#42; if you are using animations instead of transitions you should configure as follows:
 *     animation-delay: 0.1s;
 *     animation-duration: 0s; &#42;/
 * }
 * .my-animation.ng-enter.ng-enter-active {
 *   /&#42; standard transition styles &#42;/
 *   opacity:1;
 * }
 * ```
 *
 * Staggering animations work by default in ngRepeat (so long as the CSS class is defined). Outside of ngRepeat, to use staggering animations
 * on your own, they can be triggered by firing multiple calls to the same event on $animate. However, the restrictions surrounding this
 * are that each of the elements must have the same CSS className value as well as the same parent element. A stagger operation
 * will also be reset if one or more animation frames have passed since the multiple calls to `$animate` were fired.
 *
 * The following code will issue the **ng-leave-stagger** event on the element provided:
 *
 * ```js
 * var kids = parent.children();
 *
 * $animate.leave(kids[0]); //stagger index=0
 * $animate.leave(kids[1]); //stagger index=1
 * $animate.leave(kids[2]); //stagger index=2
 * $animate.leave(kids[3]); //stagger index=3
 * $animate.leave(kids[4]); //stagger index=4
 *
 * window.requestAnimationFrame(function() {
 *   //stagger has reset itself
 *   $animate.leave(kids[5]); //stagger index=0
 *   $animate.leave(kids[6]); //stagger index=1
 *
 *   $scope.$digest();
 * });
 * ```
 *
 * Stagger animations are currently only supported within CSS-defined animations.
 *
 * ### The `ng-animate` CSS class
 *
 * When ngAnimate is animating an element it will apply the `ng-animate` CSS class to the element for the duration of the animation.
 * This is a temporary CSS class and it will be removed once the animation is over (for both JavaScript and CSS-based animations).
 *
 * Therefore, animations can be applied to an element using this temporary class directly via CSS.
 *
 * ```css
 * .zipper.ng-animate {
 *   transition:0.5s linear all;
 * }
 * .zipper.ng-enter {
 *   opacity:0;
 * }
 * .zipper.ng-enter.ng-enter-active {
 *   opacity:1;
 * }
 * .zipper.ng-leave {
 *   opacity:1;
 * }
 * .zipper.ng-leave.ng-leave-active {
 *   opacity:0;
 * }
 * ```
 *
 * (Note that the `ng-animate` CSS class is reserved and it cannot be applied on an element directly since ngAnimate will always remove
 * the CSS class once an animation has completed.)
 *
 *
 * ### The `ng-[event]-prepare` class
 *
 * This is a special class that can be used to prevent unwanted flickering / flash of content before
 * the actual animation starts. The class is added as soon as an animation is initialized, but removed
 * before the actual animation starts (after waiting for a $digest).
 * It is also only added for *structural* animations (`enter`, `move`, and `leave`).
 *
 * In practice, flickering can appear when nesting elements with structural animations such as `ngIf`
 * into elements that have class-based animations such as `ngClass`.
 *
 * ```html
 * <div ng-class="{red: myProp}">
 *   <div ng-class="{blue: myProp}">
 *     <div class="message" ng-if="myProp"></div>
 *   </div>
 * </div>
 * ```
 *
 * It is possible that during the `enter` animation, the `.message` div will be briefly visible before it starts animating.
 * In that case, you can add styles to the CSS that make sure the element stays hidden before the animation starts:
 *
 * ```css
 * .message.ng-enter-prepare {
 *   opacity: 0;
 * }
 * ```
 *
 * ### Animating between value changes
 *
 * Sometimes you need to animate between different expression states, whose values
 * don't necessary need to be known or referenced in CSS styles.
 * Unless possible with another {@link ngAnimate#directive-support "animation aware" directive},
 * that specific use case can always be covered with {@link ngAnimate.directive:ngAnimateSwap} as
 * can be seen in {@link ngAnimate.directive:ngAnimateSwap#examples this example}.
 *
 * Note that {@link ngAnimate.directive:ngAnimateSwap} is a *structural directive*, which means it
 * creates a new instance of the element (including any other/child directives it may have) and
 * links it to a new scope every time *swap* happens. In some cases this might not be desirable
 * (e.g. for performance reasons, or when you wish to retain internal state on the original
 * element instance).
 *
 * ## JavaScript-based Animations
 *
 * ngAnimate also allows for animations to be consumed by JavaScript code. The approach is similar to CSS-based animations (where there is a shared
 * CSS class that is referenced in our HTML code) but in addition we need to register the JavaScript animation on the module. By making use of the
 * `module.animation()` module function we can register the animation.
 *
 * Let's see an example of a enter/leave animation using `ngRepeat`:
 *
 * ```html
 * <div ng-repeat="item in items" class="slide">
 *   {{ item }}
 * </div>
 * ```
 *
 * See the **slide** CSS class? Let's use that class to define an animation that we'll structure in our module code by using `module.animation`:
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     // make note that other events (like addClass/removeClass)
 *     // have different function input parameters
 *     enter: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *
 *       // remember to call doneFn so that AngularJS
 *       // knows that the animation has concluded
 *     },
 *
 *     move: function(element, doneFn) {
 *       jQuery(element).fadeIn(1000, doneFn);
 *     },
 *
 *     leave: function(element, doneFn) {
 *       jQuery(element).fadeOut(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing about JS-based animations is that we can inject other services and make use of advanced animation libraries such as
 * greensock.js and velocity.js.
 *
 * If our animation code class-based (meaning that something like `ngClass`, `ngHide` and `ngShow` triggers it) then we can still define
 * our animations inside of the same registered animation, however, the function input arguments are a bit different:
 *
 * ```html
 * <div ng-class="color" class="colorful">
 *   this box is moody
 * </div>
 * <button ng-click="color='red'">Change to red</button>
 * <button ng-click="color='blue'">Change to blue</button>
 * <button ng-click="color='green'">Change to green</button>
 * ```
 *
 * ```js
 * myModule.animation('.colorful', [function() {
 *   return {
 *     addClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     removeClass: function(element, className, doneFn) {
 *       // do some cool animation and call the doneFn
 *     },
 *     setClass: function(element, addedClass, removedClass, doneFn) {
 *       // do some cool animation and call the doneFn
 *     }
 *   }
 * }]);
 * ```
 *
 * ## CSS + JS Animations Together
 *
 * AngularJS 1.4 and higher has taken steps to make the amalgamation of CSS and JS animations more flexible. However, unlike earlier versions of AngularJS,
 * defining CSS and JS animations to work off of the same CSS class will not work anymore. Therefore the example below will only result in **JS animations taking
 * charge of the animation**:
 *
 * ```html
 * <div ng-if="bool" class="slide">
 *   Slide in and out
 * </div>
 * ```
 *
 * ```js
 * myModule.animation('.slide', [function() {
 *   return {
 *     enter: function(element, doneFn) {
 *       jQuery(element).slideIn(1000, doneFn);
 *     }
 *   }
 * }]);
 * ```
 *
 * ```css
 * .slide.ng-enter {
 *   transition:0.5s linear all;
 *   transform:translateY(-100px);
 * }
 * .slide.ng-enter.ng-enter-active {
 *   transform:translateY(0);
 * }
 * ```
 *
 * Does this mean that CSS and JS animations cannot be used together? Do JS-based animations always have higher priority? We can make up for the
 * lack of CSS animations by using the `$animateCss` service to trigger our own tweaked-out, CSS-based animations directly from
 * our own JS-based animation code:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
*        // this will trigger `.slide.ng-enter` and `.slide.ng-enter-active`.
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * The nice thing here is that we can save bandwidth by sticking to our CSS-based animation code and we don't need to rely on a 3rd-party animation framework.
 *
 * The `$animateCss` service is very powerful since we can feed in all kinds of extra properties that will be evaluated and fed into a CSS transition or
 * keyframe animation. For example if we wanted to animate the height of an element while adding and removing classes then we can do so by providing that
 * data into `$animateCss` directly:
 *
 * ```js
 * myModule.animation('.slide', ['$animateCss', function($animateCss) {
 *   return {
 *     enter: function(element) {
 *       return $animateCss(element, {
 *         event: 'enter',
 *         structural: true,
 *         addClass: 'maroon-setting',
 *         from: { height:0 },
 *         to: { height: 200 }
 *       });
 *     }
 *   }
 * }]);
 * ```
 *
 * Now we can fill in the rest via our transition CSS code:
 *
 * ```css
 * /&#42; the transition tells ngAnimate to make the animation happen &#42;/
 * .slide.ng-enter { transition:0.5s linear all; }
 *
 * /&#42; this extra CSS class will be absorbed into the transition
 * since the $animateCss code is adding the class &#42;/
 * .maroon-setting { background:red; }
 * ```
 *
 * And `$animateCss` will figure out the rest. Just make sure to have the `done()` callback fire the `doneFn` function to signal when the animation is over.
 *
 * To learn more about what's possible be sure to visit the {@link ngAnimate.$animateCss $animateCss service}.
 *
 * ## Animation Anchoring (via `ng-animate-ref`)
 *
 * ngAnimate in AngularJS 1.4 comes packed with the ability to cross-animate elements between
 * structural areas of an application (like views) by pairing up elements using an attribute
 * called `ng-animate-ref`.
 *
 * Let's say for example we have two views that are managed by `ng-view` and we want to show
 * that there is a relationship between two components situated in within these views. By using the
 * `ng-animate-ref` attribute we can identify that the two components are paired together and we
 * can then attach an animation, which is triggered when the view changes.
 *
 * Say for example we have the following template code:
 *
 * ```html
 * <!-- index.html -->
 * <div ng-view class="view-animation">
 * </div>
 *
 * <!-- home.html -->
 * <a href="#/banner-page">
 *   <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * </a>
 *
 * <!-- banner-page.html -->
 * <img src="./banner.jpg" class="banner" ng-animate-ref="banner">
 * ```
 *
 * Now, when the view changes (once the link is clicked), ngAnimate will examine the
 * HTML contents to see if there is a match reference between any components in the view
 * that is leaving and the view that is entering. It will scan both the view which is being
 * removed (leave) and inserted (enter) to see if there are any paired DOM elements that
 * contain a matching ref value.
 *
 * The two images match since they share the same ref value. ngAnimate will now create a
 * transport element (which is a clone of the first image element) and it will then attempt
 * to animate to the position of the second image element in the next view. For the animation to
 * work a special CSS class called `ng-anchor` will be added to the transported element.
 *
 * We can now attach a transition onto the `.banner.ng-anchor` CSS class and then
 * ngAnimate will handle the entire transition for us as well as the addition and removal of
 * any changes of CSS classes between the elements:
 *
 * ```css
 * .banner.ng-anchor {
 *   /&#42; this animation will last for 1 second since there are
 *          two phases to the animation (an `in` and an `out` phase) &#42;/
 *   transition:0.5s linear all;
 * }
 * ```
 *
 * We also **must** include animations for the views that are being entered and removed
 * (otherwise anchoring wouldn't be possible since the new view would be inserted right away).
 *
 * ```css
 * .view-animation.ng-enter, .view-animation.ng-leave {
 *   transition:0.5s linear all;
 *   position:fixed;
 *   left:0;
 *   top:0;
 *   width:100%;
 * }
 * .view-animation.ng-enter {
 *   transform:translateX(100%);
 * }
 * .view-animation.ng-leave,
 * .view-animation.ng-enter.ng-enter-active {
 *   transform:translateX(0%);
 * }
 * .view-animation.ng-leave.ng-leave-active {
 *   transform:translateX(-100%);
 * }
 * ```
 *
 * Now we can jump back to the anchor animation. When the animation happens, there are two stages that occur:
 * an `out` and an `in` stage. The `out` stage happens first and that is when the element is animated away
 * from its origin. Once that animation is over then the `in` stage occurs which animates the
 * element to its destination. The reason why there are two animations is to give enough time
 * for the enter animation on the new element to be ready.
 *
 * The example above sets up a transition for both the in and out phases, but we can also target the out or
 * in phases directly via `ng-anchor-out` and `ng-anchor-in`.
 *
 * ```css
 * .banner.ng-anchor-out {
 *   transition: 0.5s linear all;
 *
 *   /&#42; the scale will be applied during the out animation,
 *          but will be animated away when the in animation runs &#42;/
 *   transform: scale(1.2);
 * }
 *
 * .banner.ng-anchor-in {
 *   transition: 1s linear all;
 * }
 * ```
 *
 *
 *
 *
 * ### Anchoring Demo
 *
  <example module="anchoringExample"
           name="anchoringExample"
           id="anchoringExample"
           deps="angular-animate.js;angular-route.js"
           animations="true">
    <file name="index.html">
      <a href="#!/">Home</a>
      <hr />
      <div class="view-container">
        <div ng-view class="view"></div>
      </div>
    </file>
    <file name="script.js">
      angular.module('anchoringExample', ['ngAnimate', 'ngRoute'])
        .config(['$routeProvider', function($routeProvider) {
          $routeProvider.when('/', {
            templateUrl: 'home.html',
            controller: 'HomeController as home'
          });
          $routeProvider.when('/profile/:id', {
            templateUrl: 'profile.html',
            controller: 'ProfileController as profile'
          });
        }])
        .run(['$rootScope', function($rootScope) {
          $rootScope.records = [
            { id: 1, title: 'Miss Beulah Roob' },
            { id: 2, title: 'Trent Morissette' },
            { id: 3, title: 'Miss Ava Pouros' },
            { id: 4, title: 'Rod Pouros' },
            { id: 5, title: 'Abdul Rice' },
            { id: 6, title: 'Laurie Rutherford Sr.' },
            { id: 7, title: 'Nakia McLaughlin' },
            { id: 8, title: 'Jordon Blanda DVM' },
            { id: 9, title: 'Rhoda Hand' },
            { id: 10, title: 'Alexandrea Sauer' }
          ];
        }])
        .controller('HomeController', [function() {
          //empty
        }])
        .controller('ProfileController', ['$rootScope', '$routeParams',
            function ProfileController($rootScope, $routeParams) {
          var index = parseInt($routeParams.id, 10);
          var record = $rootScope.records[index - 1];

          this.title = record.title;
          this.id = record.id;
        }]);
    </file>
    <file name="home.html">
      <h2>Welcome to the home page</h1>
      <p>Please click on an element</p>
      <a class="record"
         ng-href="#!/profile/{{ record.id }}"
         ng-animate-ref="{{ record.id }}"
         ng-repeat="record in records">
        {{ record.title }}
      </a>
    </file>
    <file name="profile.html">
      <div class="profile record" ng-animate-ref="{{ profile.id }}">
        {{ profile.title }}
      </div>
    </file>
    <file name="animations.css">
      .record {
        display:block;
        font-size:20px;
      }
      .profile {
        background:black;
        color:white;
        font-size:100px;
      }
      .view-container {
        position:relative;
      }
      .view-container > .view.ng-animate {
        position:absolute;
        top:0;
        left:0;
        width:100%;
        min-height:500px;
      }
      .view.ng-enter, .view.ng-leave,
      .record.ng-anchor {
        transition:0.5s linear all;
      }
      .view.ng-enter {
        transform:translateX(100%);
      }
      .view.ng-enter.ng-enter-active, .view.ng-leave {
        transform:translateX(0%);
      }
      .view.ng-leave.ng-leave-active {
        transform:translateX(-100%);
      }
      .record.ng-anchor-out {
        background:red;
      }
    </file>
  </example>
 *
 * ### How is the element transported?
 *
 * When an anchor animation occurs, ngAnimate will clone the starting element and position it exactly where the starting
 * element is located on screen via absolute positioning. The cloned element will be placed inside of the root element
 * of the application (where ng-app was defined) and all of the CSS classes of the starting element will be applied. The
 * element will then animate into the `out` and `in` animations and will eventually reach the coordinates and match
 * the dimensions of the destination element. During the entire animation a CSS class of `.ng-animate-shim` will be applied
 * to both the starting and destination elements in order to hide them from being visible (the CSS styling for the class
 * is: `visibility:hidden`). Once the anchor reaches its destination then it will be removed and the destination element
 * will become visible since the shim class will be removed.
 *
 * ### How is the morphing handled?
 *
 * CSS Anchoring relies on transitions and keyframes and the internal code is intelligent enough to figure out
 * what CSS classes differ between the starting element and the destination element. These different CSS classes
 * will be added/removed on the anchor element and a transition will be applied (the transition that is provided
 * in the anchor class). Long story short, ngAnimate will figure out what classes to add and remove which will
 * make the transition of the element as smooth and automatic as possible. Be sure to use simple CSS classes that
 * do not rely on DOM nesting structure so that the anchor element appears the same as the starting element (since
 * the cloned element is placed inside of root element which is likely close to the body element).
 *
 * Note that if the root element is on the `<html>` element then the cloned node will be placed inside of body.
 *
 *
 * ## Using $animate in your directive code
 *
 * So far we've explored how to feed in animations into an AngularJS application, but how do we trigger animations within our own directives in our application?
 * By injecting the `$animate` service into our directive code, we can trigger structural and class-based hooks which can then be consumed by animations. Let's
 * imagine we have a greeting box that shows and hides itself when the data changes
 *
 * ```html
 * <greeting-box active="onOrOff">Hi there</greeting-box>
 * ```
 *
 * ```js
 * ngModule.directive('greetingBox', ['$animate', function($animate) {
 *   return function(scope, element, attrs) {
 *     attrs.$observe('active', function(value) {
 *       value ? $animate.addClass(element, 'on') : $animate.removeClass(element, 'on');
 *     });
 *   });
 * }]);
 * ```
 *
 * Now the `on` CSS class is added and removed on the greeting box component. Now if we add a CSS class on top of the greeting box element
 * in our HTML code then we can trigger a CSS or JS animation to happen.
 *
 * ```css
 * /&#42; normally we would create a CSS class to reference on the element &#42;/
 * greeting-box.on { transition:0.5s linear all; background:green; color:white; }
 * ```
 *
 * The `$animate` service contains a variety of other methods like `enter`, `leave`, `animate` and `setClass`. To learn more about what's
 * possible be sure to visit the {@link ng.$animate $animate service API page}.
 *
 *
 * ## Callbacks and Promises
 *
 * When `$animate` is called it returns a promise that can be used to capture when the animation has ended. Therefore if we were to trigger
 * an animation (within our directive code) then we can continue performing directive and scope related activities after the animation has
 * ended by chaining onto the returned promise that animation method returns.
 *
 * ```js
 * // somewhere within the depths of the directive
 * $animate.enter(element, parent).then(function() {
 *   //the animation has completed
 * });
 * ```
 *
 * (Note that earlier versions of AngularJS prior to v1.4 required the promise code to be wrapped using `$scope.$apply(...)`. This is not the case
 * anymore.)
 *
 * In addition to the animation promise, we can also make use of animation-related callbacks within our directives and controller code by registering
 * an event listener using the `$animate` service. Let's say for example that an animation was triggered on our view
 * routing controller to hook into that:
 *
 * ```js
 * ngModule.controller('HomePageController', ['$animate', function($animate) {
 *   $animate.on('enter', ngViewElement, function(element) {
 *     // the animation for this route has completed
 *   }]);
 * }])
 * ```
 *
 * (Note that you will need to trigger a digest within the callback to get AngularJS to notice any scope-related changes.)
 */

var copy;
var extend;
var forEach;
var isArray;
var isDefined;
var isElement;
var isFunction;
var isObject;
var isString;
var isUndefined;
var jqLite;
var noop;

/**
 * @ngdoc service
 * @name $animate
 * @kind object
 *
 * @description
 * The ngAnimate `$animate` service documentation is the same for the core `$animate` service.
 *
 * Click here {@link ng.$animate to learn more about animations with `$animate`}.
 */
angular.module('ngAnimate', [], function initAngularHelpers() {
  // Access helpers from AngularJS core.
  // Do it inside a `config` block to ensure `window.angular` is available.
  noop        = angular.noop;
  copy        = angular.copy;
  extend      = angular.extend;
  jqLite      = angular.element;
  forEach     = angular.forEach;
  isArray     = angular.isArray;
  isString    = angular.isString;
  isObject    = angular.isObject;
  isUndefined = angular.isUndefined;
  isDefined   = angular.isDefined;
  isFunction  = angular.isFunction;
  isElement   = angular.isElement;
})
  .info({ angularVersion: '1.8.3' })
  .directive('ngAnimateSwap', ngAnimateSwapDirective)

  .directive('ngAnimateChildren', $$AnimateChildrenDirective)
  .factory('$$rAFScheduler', $$rAFSchedulerFactory)

  .provider('$$animateQueue', $$AnimateQueueProvider)
  .provider('$$animateCache', $$AnimateCacheProvider)
  .provider('$$animation', $$AnimationProvider)

  .provider('$animateCss', $AnimateCssProvider)
  .provider('$$animateCssDriver', $$AnimateCssDriverProvider)

  .provider('$$animateJs', $$AnimateJsProvider)
  .provider('$$animateJsDriver', $$AnimateJsDriverProvider);


})(window, window.angular);

/**
 * @license AngularJS v1.6.10
 * (c) 2010-2018 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngCookies
 * @description
 *
 * The `ngCookies` module provides a convenient wrapper for reading and writing browser cookies.
 *
 * See {@link ngCookies.$cookies `$cookies`} for usage.
 */


angular.module('ngCookies', ['ng']).
  info({ angularVersion: '1.6.10' }).
  /**
   * @ngdoc provider
   * @name $cookiesProvider
   * @description
   * Use `$cookiesProvider` to change the default behavior of the {@link ngCookies.$cookies $cookies} service.
   * */
   provider('$cookies', [/** @this */function $CookiesProvider() {
    /**
     * @ngdoc property
     * @name $cookiesProvider#defaults
     * @description
     *
     * Object containing default options to pass when setting cookies.
     *
     * The object may have following properties:
     *
     * - **path** - `{string}` - The cookie will be available only for this path and its
     *   sub-paths. By default, this is the URL that appears in your `<base>` tag.
     * - **domain** - `{string}` - The cookie will be available only for this domain and
     *   its sub-domains. For security reasons the user agent will not accept the cookie
     *   if the current domain is not a sub-domain of this domain or equal to it.
     * - **expires** - `{string|Date}` - String of the form "Wdy, DD Mon YYYY HH:MM:SS GMT"
     *   or a Date object indicating the exact date/time this cookie will expire.
     * - **secure** - `{boolean}` - If `true`, then the cookie will only be available through a
     *   secured connection.
     *
     * Note: By default, the address that appears in your `<base>` tag will be used as the path.
     * This is important so that cookies will be visible for all routes when html5mode is enabled.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesProviderExample', ['ngCookies'])
     *   .config(['$cookiesProvider', function($cookiesProvider) {
     *     // Setting default options
     *     $cookiesProvider.defaults.domain = 'foo.com';
     *     $cookiesProvider.defaults.secure = true;
     *   }]);
     * ```
     **/
    var defaults = this.defaults = {};

    function calcOptions(options) {
      return options ? angular.extend({}, defaults, options) : defaults;
    }

    /**
     * @ngdoc service
     * @name $cookies
     *
     * @description
     * Provides read/write access to browser's cookies.
     *
     * <div class="alert alert-info">
     * Up until AngularJS 1.3, `$cookies` exposed properties that represented the
     * current browser cookie values. In version 1.4, this behavior has changed, and
     * `$cookies` now provides a standard api of getters, setters etc.
     * </div>
     *
     * Requires the {@link ngCookies `ngCookies`} module to be installed.
     *
     * @example
     *
     * ```js
     * angular.module('cookiesExample', ['ngCookies'])
     *   .controller('ExampleController', ['$cookies', function($cookies) {
     *     // Retrieving a cookie
     *     var favoriteCookie = $cookies.get('myFavorite');
     *     // Setting a cookie
     *     $cookies.put('myFavorite', 'oatmeal');
     *   }]);
     * ```
     */
    this.$get = ['$$cookieReader', '$$cookieWriter', function($$cookieReader, $$cookieWriter) {
      return {
        /**
         * @ngdoc method
         * @name $cookies#get
         *
         * @description
         * Returns the value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {string} Raw cookie value.
         */
        get: function(key) {
          return $$cookieReader()[key];
        },

        /**
         * @ngdoc method
         * @name $cookies#getObject
         *
         * @description
         * Returns the deserialized value of given cookie key
         *
         * @param {string} key Id to use for lookup.
         * @returns {Object} Deserialized cookie value.
         */
        getObject: function(key) {
          var value = this.get(key);
          return value ? angular.fromJson(value) : value;
        },

        /**
         * @ngdoc method
         * @name $cookies#getAll
         *
         * @description
         * Returns a key value object with all the cookies
         *
         * @returns {Object} All cookies
         */
        getAll: function() {
          return $$cookieReader();
        },

        /**
         * @ngdoc method
         * @name $cookies#put
         *
         * @description
         * Sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {string} value Raw value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        put: function(key, value, options) {
          $$cookieWriter(key, value, calcOptions(options));
        },

        /**
         * @ngdoc method
         * @name $cookies#putObject
         *
         * @description
         * Serializes and sets a value for given cookie key
         *
         * @param {string} key Id for the `value`.
         * @param {Object} value Value to be stored.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        putObject: function(key, value, options) {
          this.put(key, angular.toJson(value), options);
        },

        /**
         * @ngdoc method
         * @name $cookies#remove
         *
         * @description
         * Remove given cookie
         *
         * @param {string} key Id of the key-value pair to delete.
         * @param {Object=} options Options object.
         *    See {@link ngCookies.$cookiesProvider#defaults $cookiesProvider.defaults}
         */
        remove: function(key, options) {
          $$cookieWriter(key, undefined, calcOptions(options));
        }
      };
    }];
  }]);

angular.module('ngCookies').
/**
 * @ngdoc service
 * @name $cookieStore
 * @deprecated
 * sinceVersion="v1.4.0"
 * Please use the {@link ngCookies.$cookies `$cookies`} service instead.
 *
 * @requires $cookies
 *
 * @description
 * Provides a key-value (string-object) storage, that is backed by session cookies.
 * Objects put or retrieved from this storage are automatically serialized or
 * deserialized by AngularJS's `toJson`/`fromJson`.
 *
 * Requires the {@link ngCookies `ngCookies`} module to be installed.
 *
 * @example
 *
 * ```js
 * angular.module('cookieStoreExample', ['ngCookies'])
 *   .controller('ExampleController', ['$cookieStore', function($cookieStore) {
 *     // Put cookie
 *     $cookieStore.put('myFavorite','oatmeal');
 *     // Get cookie
 *     var favoriteCookie = $cookieStore.get('myFavorite');
 *     // Removing a cookie
 *     $cookieStore.remove('myFavorite');
 *   }]);
 * ```
 */
 factory('$cookieStore', ['$cookies', function($cookies) {

    return {
      /**
       * @ngdoc method
       * @name $cookieStore#get
       *
       * @description
       * Returns the value of given cookie key
       *
       * @param {string} key Id to use for lookup.
       * @returns {Object} Deserialized cookie value, undefined if the cookie does not exist.
       */
      get: function(key) {
        return $cookies.getObject(key);
      },

      /**
       * @ngdoc method
       * @name $cookieStore#put
       *
       * @description
       * Sets a value for given cookie key
       *
       * @param {string} key Id for the `value`.
       * @param {Object} value Value to be stored.
       */
      put: function(key, value) {
        $cookies.putObject(key, value);
      },

      /**
       * @ngdoc method
       * @name $cookieStore#remove
       *
       * @description
       * Remove given cookie
       *
       * @param {string} key Id of the key-value pair to delete.
       */
      remove: function(key) {
        $cookies.remove(key);
      }
    };

  }]);

/**
 * @name $$cookieWriter
 * @requires $document
 *
 * @description
 * This is a private service for writing cookies
 *
 * @param {string} name Cookie name
 * @param {string=} value Cookie value (if undefined, cookie will be deleted)
 * @param {Object=} options Object with options that need to be stored for the cookie.
 */
function $$CookieWriter($document, $log, $browser) {
  var cookiePath = $browser.baseHref();
  var rawDocument = $document[0];

  function buildCookieString(name, value, options) {
    var path, expires;
    options = options || {};
    expires = options.expires;
    path = angular.isDefined(options.path) ? options.path : cookiePath;
    if (angular.isUndefined(value)) {
      expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
      value = '';
    }
    if (angular.isString(expires)) {
      expires = new Date(expires);
    }

    var str = encodeURIComponent(name) + '=' + encodeURIComponent(value);
    str += path ? ';path=' + path : '';
    str += options.domain ? ';domain=' + options.domain : '';
    str += expires ? ';expires=' + expires.toUTCString() : '';
    str += options.secure ? ';secure' : '';

    // per http://www.ietf.org/rfc/rfc2109.txt browser must allow at minimum:
    // - 300 cookies
    // - 20 cookies per unique domain
    // - 4096 bytes per cookie
    var cookieLength = str.length + 1;
    if (cookieLength > 4096) {
      $log.warn('Cookie \'' + name +
        '\' possibly not set or overflowed because it was too large (' +
        cookieLength + ' > 4096 bytes)!');
    }

    return str;
  }

  return function(name, value, options) {
    rawDocument.cookie = buildCookieString(name, value, options);
  };
}

$$CookieWriter.$inject = ['$document', '$log', '$browser'];

angular.module('ngCookies').provider('$$cookieWriter', /** @this */ function $$CookieWriterProvider() {
  this.$get = $$CookieWriter;
});


})(window, window.angular);

/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngTouch
 * @description
 *
 * The `ngTouch` module provides helpers for touch-enabled devices.
 * The implementation is based on jQuery Mobile touch event handling
 * ([jquerymobile.com](http://jquerymobile.com/)). *
 *
 * See {@link ngTouch.$swipe `$swipe`} for usage.
 *
 * @deprecated
 * sinceVersion="1.7.0"
 * The ngTouch module with the {@link ngTouch.$swipe `$swipe`} service and
 * the {@link ngTouch.ngSwipeLeft} and {@link ngTouch.ngSwipeRight} directives are
 * deprecated. Instead, stand-alone libraries for touch handling and gesture interaction
 * should be used, for example [HammerJS](https://hammerjs.github.io/) (which is also used by
 * Angular).
 */

// define ngTouch module
/* global ngTouch */
var ngTouch = angular.module('ngTouch', []);

ngTouch.info({ angularVersion: '1.8.3' });

function nodeName_(element) {
  return angular.$$lowercase(element.nodeName || (element[0] && element[0].nodeName));
}

/* global ngTouch: false */

    /**
     * @ngdoc service
     * @name $swipe
     *
     * @deprecated
     * sinceVersion="1.7.0"
     *
     * See the {@link ngTouch module} documentation for more information.
     *
     * @description
     * The `$swipe` service is a service that abstracts the messier details of hold-and-drag swipe
     * behavior, to make implementing swipe-related directives more convenient.
     *
     * Requires the {@link ngTouch `ngTouch`} module to be installed.
     *
     * `$swipe` is used by the `ngSwipeLeft` and `ngSwipeRight` directives in `ngTouch`.
     *
     * # Usage
     * The `$swipe` service is an object with a single method: `bind`. `bind` takes an element
     * which is to be watched for swipes, and an object with four handler functions. See the
     * documentation for `bind` below.
     */

ngTouch.factory('$swipe', [function() {
  // The total distance in any direction before we make the call on swipe vs. scroll.
  var MOVE_BUFFER_RADIUS = 10;

  var POINTER_EVENTS = {
    'mouse': {
      start: 'mousedown',
      move: 'mousemove',
      end: 'mouseup'
    },
    'touch': {
      start: 'touchstart',
      move: 'touchmove',
      end: 'touchend',
      cancel: 'touchcancel'
    },
    'pointer': {
      start: 'pointerdown',
      move: 'pointermove',
      end: 'pointerup',
      cancel: 'pointercancel'
    }
  };

  function getCoordinates(event) {
    var originalEvent = event.originalEvent || event;
    var touches = originalEvent.touches && originalEvent.touches.length ? originalEvent.touches : [originalEvent];
    var e = (originalEvent.changedTouches && originalEvent.changedTouches[0]) || touches[0];

    return {
      x: e.clientX,
      y: e.clientY
    };
  }

  function getEvents(pointerTypes, eventType) {
    var res = [];
    angular.forEach(pointerTypes, function(pointerType) {
      var eventName = POINTER_EVENTS[pointerType][eventType];
      if (eventName) {
        res.push(eventName);
      }
    });
    return res.join(' ');
  }

  return {
    /**
     * @ngdoc method
     * @name $swipe#bind
     *
     * @description
     * The main method of `$swipe`. It takes an element to be watched for swipe motions, and an
     * object containing event handlers.
     * The pointer types that should be used can be specified via the optional
     * third argument, which is an array of strings `'mouse'`, `'touch'` and `'pointer'`. By default,
     * `$swipe` will listen for `mouse`, `touch` and `pointer` events.
     *
     * The four events are `start`, `move`, `end`, and `cancel`. `start`, `move`, and `end`
     * receive as a parameter a coordinates object of the form `{ x: 150, y: 310 }` and the raw
     * `event`. `cancel` receives the raw `event` as its single parameter.
     *
     * `start` is called on either `mousedown`, `touchstart` or `pointerdown`. After this event, `$swipe` is
     * watching for `touchmove`, `mousemove` or `pointermove` events. These events are ignored until the total
     * distance moved in either dimension exceeds a small threshold.
     *
     * Once this threshold is exceeded, either the horizontal or vertical delta is greater.
     * - If the horizontal distance is greater, this is a swipe and `move` and `end` events follow.
     * - If the vertical distance is greater, this is a scroll, and we let the browser take over.
     *   A `cancel` event is sent.
     *
     * `move` is called on `mousemove`, `touchmove` and `pointermove` after the above logic has determined that
     * a swipe is in progress.
     *
     * `end` is called when a swipe is successfully completed with a `touchend`, `mouseup` or `pointerup`.
     *
     * `cancel` is called either on a `touchcancel` or `pointercancel`  from the browser, or when we begin scrolling
     * as described above.
     *
     */
    bind: function(element, eventHandlers, pointerTypes) {
      // Absolute total movement, used to control swipe vs. scroll.
      var totalX, totalY;
      // Coordinates of the start position.
      var startCoords;
      // Last event's position.
      var lastPos;
      // Whether a swipe is active.
      var active = false;

      pointerTypes = pointerTypes || ['mouse', 'touch', 'pointer'];
      element.on(getEvents(pointerTypes, 'start'), function(event) {
        startCoords = getCoordinates(event);
        active = true;
        totalX = 0;
        totalY = 0;
        lastPos = startCoords;
        if (eventHandlers['start']) {
          eventHandlers['start'](startCoords, event);
        }
      });
      var events = getEvents(pointerTypes, 'cancel');
      if (events) {
        element.on(events, function(event) {
          active = false;
          if (eventHandlers['cancel']) {
            eventHandlers['cancel'](event);
          }
        });
      }

      element.on(getEvents(pointerTypes, 'move'), function(event) {
        if (!active) return;

        // Android will send a touchcancel if it thinks we're starting to scroll.
        // So when the total distance (+ or - or both) exceeds 10px in either direction,
        // we either:
        // - On totalX > totalY, we send preventDefault() and treat this as a swipe.
        // - On totalY > totalX, we let the browser handle it as a scroll.

        if (!startCoords) return;
        var coords = getCoordinates(event);

        totalX += Math.abs(coords.x - lastPos.x);
        totalY += Math.abs(coords.y - lastPos.y);

        lastPos = coords;

        if (totalX < MOVE_BUFFER_RADIUS && totalY < MOVE_BUFFER_RADIUS) {
          return;
        }

        // One of totalX or totalY has exceeded the buffer, so decide on swipe vs. scroll.
        if (totalY > totalX) {
          // Allow native scrolling to take over.
          active = false;
          if (eventHandlers['cancel']) {
            eventHandlers['cancel'](event);
          }
          return;
        } else {
          // Prevent the browser from scrolling.
          event.preventDefault();
          if (eventHandlers['move']) {
            eventHandlers['move'](coords, event);
          }
        }
      });

      element.on(getEvents(pointerTypes, 'end'), function(event) {
        if (!active) return;
        active = false;
        if (eventHandlers['end']) {
          eventHandlers['end'](getCoordinates(event), event);
        }
      });
    }
  };
}]);

/* global ngTouch: false */

/**
 * @ngdoc directive
 * @name ngSwipeLeft
 *
 * @deprecated
 * sinceVersion="1.7.0"
 *
 * See the {@link ngTouch module} documentation for more information.
 *
 * @description
 * Specify custom behavior when an element is swiped to the left on a touchscreen device.
 * A leftward swipe is a quick, right-to-left slide of the finger.
 * Though ngSwipeLeft is designed for touch-based devices, it will work with a mouse click and drag
 * too.
 *
 * To disable the mouse click and drag functionality, add `ng-swipe-disable-mouse` to
 * the `ng-swipe-left` or `ng-swipe-right` DOM Element.
 *
 * Requires the {@link ngTouch `ngTouch`} module to be installed.
 *
 * @element ANY
 * @param {expression} ngSwipeLeft {@link guide/expression Expression} to evaluate
 * upon left swipe. (Event object is available as `$event`)
 *
 * @example
    <example module="ngSwipeLeftExample" deps="angular-touch.js" name="ng-swipe-left">
      <file name="index.html">
        <div ng-show="!showActions" ng-swipe-left="showActions = true">
          Some list content, like an email in the inbox
        </div>
        <div ng-show="showActions" ng-swipe-right="showActions = false">
          <button ng-click="reply()">Reply</button>
          <button ng-click="delete()">Delete</button>
        </div>
      </file>
      <file name="script.js">
        angular.module('ngSwipeLeftExample', ['ngTouch']);
      </file>
    </example>
 */

/**
 * @ngdoc directive
 * @name ngSwipeRight
 *
 * @deprecated
 * sinceVersion="1.7.0"
 *
 * See the {@link ngTouch module} documentation for more information.
 *
 * @description
 * Specify custom behavior when an element is swiped to the right on a touchscreen device.
 * A rightward swipe is a quick, left-to-right slide of the finger.
 * Though ngSwipeRight is designed for touch-based devices, it will work with a mouse click and drag
 * too.
 *
 * Requires the {@link ngTouch `ngTouch`} module to be installed.
 *
 * @element ANY
 * @param {expression} ngSwipeRight {@link guide/expression Expression} to evaluate
 * upon right swipe. (Event object is available as `$event`)
 *
 * @example
    <example module="ngSwipeRightExample" deps="angular-touch.js" name="ng-swipe-right">
      <file name="index.html">
        <div ng-show="!showActions" ng-swipe-left="showActions = true">
          Some list content, like an email in the inbox
        </div>
        <div ng-show="showActions" ng-swipe-right="showActions = false">
          <button ng-click="reply()">Reply</button>
          <button ng-click="delete()">Delete</button>
        </div>
      </file>
      <file name="script.js">
        angular.module('ngSwipeRightExample', ['ngTouch']);
      </file>
    </example>
 */

function makeSwipeDirective(directiveName, direction, eventName) {
  ngTouch.directive(directiveName, ['$parse', '$swipe', function($parse, $swipe) {
    // The maximum vertical delta for a swipe should be less than 75px.
    var MAX_VERTICAL_DISTANCE = 75;
    // Vertical distance should not be more than a fraction of the horizontal distance.
    var MAX_VERTICAL_RATIO = 0.3;
    // At least a 30px lateral motion is necessary for a swipe.
    var MIN_HORIZONTAL_DISTANCE = 30;

    return function(scope, element, attr) {
      var swipeHandler = $parse(attr[directiveName]);

      var startCoords, valid;

      function validSwipe(coords) {
        // Check that it's within the coordinates.
        // Absolute vertical distance must be within tolerances.
        // Horizontal distance, we take the current X - the starting X.
        // This is negative for leftward swipes and positive for rightward swipes.
        // After multiplying by the direction (-1 for left, +1 for right), legal swipes
        // (ie. same direction as the directive wants) will have a positive delta and
        // illegal ones a negative delta.
        // Therefore this delta must be positive, and larger than the minimum.
        if (!startCoords) return false;
        var deltaY = Math.abs(coords.y - startCoords.y);
        var deltaX = (coords.x - startCoords.x) * direction;
        return valid && // Short circuit for already-invalidated swipes.
            deltaY < MAX_VERTICAL_DISTANCE &&
            deltaX > 0 &&
            deltaX > MIN_HORIZONTAL_DISTANCE &&
            deltaY / deltaX < MAX_VERTICAL_RATIO;
      }

      var pointerTypes = ['touch'];
      if (!angular.isDefined(attr['ngSwipeDisableMouse'])) {
        pointerTypes.push('mouse');
      }
      $swipe.bind(element, {
        'start': function(coords, event) {
          startCoords = coords;
          valid = true;
        },
        'cancel': function(event) {
          valid = false;
        },
        'end': function(coords, event) {
          if (validSwipe(coords)) {
            scope.$apply(function() {
              element.triggerHandler(eventName);
              swipeHandler(scope, {$event: event});
            });
          }
        }
      }, pointerTypes);
    };
  }]);
}

// Left is negative X-coordinate, right is positive.
makeSwipeDirective('ngSwipeLeft', -1, 'swipeleft');
makeSwipeDirective('ngSwipeRight', 1, 'swiperight');



})(window, window.angular);

/**
 * @license AngularJS v1.6.10
 * (c) 2010-2018 Google, Inc. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *     Any commits to this file should be reviewed with security in mind.  *
 *   Changes to this file can potentially create security vulnerabilities. *
 *          An approval from 2 Core members with history of modifying      *
 *                         this file is required.                          *
 *                                                                         *
 *  Does the change somehow allow for arbitrary javascript to be executed? *
 *    Or allows for someone to change the prototype of built-in objects?   *
 *     Or gives undesired access to variables likes document or window?    *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

var $sanitizeMinErr = angular.$$minErr('$sanitize');
var bind;
var extend;
var forEach;
var isArray;
var isDefined;
var lowercase;
var noop;
var nodeContains;
var htmlParser;
var htmlSanitizeWriter;

/**
 * @ngdoc module
 * @name ngSanitize
 * @description
 *
 * The `ngSanitize` module provides functionality to sanitize HTML.
 *
 * See {@link ngSanitize.$sanitize `$sanitize`} for usage.
 */

/**
 * @ngdoc service
 * @name $sanitize
 * @kind function
 *
 * @description
 *   Sanitizes an html string by stripping all potentially dangerous tokens.
 *
 *   The input is sanitized by parsing the HTML into tokens. All safe tokens (from a whitelist) are
 *   then serialized back to properly escaped html string. This means that no unsafe input can make
 *   it into the returned string.
 *
 *   The whitelist for URL sanitization of attribute values is configured using the functions
 *   `aHrefSanitizationWhitelist` and `imgSrcSanitizationWhitelist` of {@link ng.$compileProvider
 *   `$compileProvider`}.
 *
 *   The input may also contain SVG markup if this is enabled via {@link $sanitizeProvider}.
 *
 * @param {string} html HTML input.
 * @returns {string} Sanitized HTML.
 *
 * @example
   <example module="sanitizeExample" deps="angular-sanitize.js" name="sanitize-service">
   <file name="index.html">
     <script>
         angular.module('sanitizeExample', ['ngSanitize'])
           .controller('ExampleController', ['$scope', '$sce', function($scope, $sce) {
             $scope.snippet =
               '<p style="color:blue">an html\n' +
               '<em onmouseover="this.textContent=\'PWN3D!\'">click here</em>\n' +
               'snippet</p>';
             $scope.deliberatelyTrustDangerousSnippet = function() {
               return $sce.trustAsHtml($scope.snippet);
             };
           }]);
     </script>
     <div ng-controller="ExampleController">
        Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <td>Directive</td>
           <td>How</td>
           <td>Source</td>
           <td>Rendered</td>
         </tr>
         <tr id="bind-html-with-sanitize">
           <td>ng-bind-html</td>
           <td>Automatically uses $sanitize</td>
           <td><pre>&lt;div ng-bind-html="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind-html="snippet"></div></td>
         </tr>
         <tr id="bind-html-with-trust">
           <td>ng-bind-html</td>
           <td>Bypass $sanitize by explicitly trusting the dangerous value</td>
           <td>
           <pre>&lt;div ng-bind-html="deliberatelyTrustDangerousSnippet()"&gt;
&lt;/div&gt;</pre>
           </td>
           <td><div ng-bind-html="deliberatelyTrustDangerousSnippet()"></div></td>
         </tr>
         <tr id="bind-default">
           <td>ng-bind</td>
           <td>Automatically escapes</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br/>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
       </div>
   </file>
   <file name="protractor.js" type="protractor">
     it('should sanitize the html snippet by default', function() {
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('<p>an html\n<em>click here</em>\nsnippet</p>');
     });

     it('should inline raw snippet if bound to a trusted value', function() {
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).
         toBe("<p style=\"color:blue\">an html\n" +
              "<em onmouseover=\"this.textContent='PWN3D!'\">click here</em>\n" +
              "snippet</p>");
     });

     it('should escape snippet without any filter', function() {
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).
         toBe("&lt;p style=\"color:blue\"&gt;an html\n" +
              "&lt;em onmouseover=\"this.textContent='PWN3D!'\"&gt;click here&lt;/em&gt;\n" +
              "snippet&lt;/p&gt;");
     });

     it('should update', function() {
       element(by.model('snippet')).clear();
       element(by.model('snippet')).sendKeys('new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-html-with-sanitize div')).getAttribute('innerHTML')).
         toBe('new <b>text</b>');
       expect(element(by.css('#bind-html-with-trust div')).getAttribute('innerHTML')).toBe(
         'new <b onclick="alert(1)">text</b>');
       expect(element(by.css('#bind-default div')).getAttribute('innerHTML')).toBe(
         "new &lt;b onclick=\"alert(1)\"&gt;text&lt;/b&gt;");
     });
   </file>
   </example>
 */


/**
 * @ngdoc provider
 * @name $sanitizeProvider
 * @this
 *
 * @description
 * Creates and configures {@link $sanitize} instance.
 */
function $SanitizeProvider() {
  var hasBeenInstantiated = false;
  var svgEnabled = false;

  this.$get = ['$$sanitizeUri', function($$sanitizeUri) {
    hasBeenInstantiated = true;
    if (svgEnabled) {
      extend(validElements, svgElements);
    }
    return function(html) {
      var buf = [];
      htmlParser(html, htmlSanitizeWriter(buf, function(uri, isImage) {
        return !/^unsafe:/.test($$sanitizeUri(uri, isImage));
      }));
      return buf.join('');
    };
  }];


  /**
   * @ngdoc method
   * @name $sanitizeProvider#enableSvg
   * @kind function
   *
   * @description
   * Enables a subset of svg to be supported by the sanitizer.
   *
   * <div class="alert alert-warning">
   *   <p>By enabling this setting without taking other precautions, you might expose your
   *   application to click-hijacking attacks. In these attacks, sanitized svg elements could be positioned
   *   outside of the containing element and be rendered over other elements on the page (e.g. a login
   *   link). Such behavior can then result in phishing incidents.</p>
   *
   *   <p>To protect against these, explicitly setup `overflow: hidden` css rule for all potential svg
   *   tags within the sanitized content:</p>
   *
   *   <br>
   *
   *   <pre><code>
   *   .rootOfTheIncludedContent svg {
   *     overflow: hidden !important;
   *   }
   *   </code></pre>
   * </div>
   *
   * @param {boolean=} flag Enable or disable SVG support in the sanitizer.
   * @returns {boolean|$sanitizeProvider} Returns the currently configured value if called
   *    without an argument or self for chaining otherwise.
   */
  this.enableSvg = function(enableSvg) {
    if (isDefined(enableSvg)) {
      svgEnabled = enableSvg;
      return this;
    } else {
      return svgEnabled;
    }
  };


  /**
   * @ngdoc method
   * @name $sanitizeProvider#addValidElements
   * @kind function
   *
   * @description
   * Extends the built-in lists of valid HTML/SVG elements, i.e. elements that are considered safe
   * and are not stripped off during sanitization. You can extend the following lists of elements:
   *
   * - `htmlElements`: A list of elements (tag names) to extend the current list of safe HTML
   *   elements. HTML elements considered safe will not be removed during sanitization. All other
   *   elements will be stripped off.
   *
   * - `htmlVoidElements`: This is similar to `htmlElements`, but marks the elements as
   *   "void elements" (similar to HTML
   *   [void elements](https://rawgit.com/w3c/html/html5.1-2/single-page.html#void-elements)). These
   *   elements have no end tag and cannot have content.
   *
   * - `svgElements`: This is similar to `htmlElements`, but for SVG elements. This list is only
   *   taken into account if SVG is {@link ngSanitize.$sanitizeProvider#enableSvg enabled} for
   *   `$sanitize`.
   *
   * <div class="alert alert-info">
   *   This method must be called during the {@link angular.Module#config config} phase. Once the
   *   `$sanitize` service has been instantiated, this method has no effect.
   * </div>
   *
   * <div class="alert alert-warning">
   *   Keep in mind that extending the built-in lists of elements may expose your app to XSS or
   *   other vulnerabilities. Be very mindful of the elements you add.
   * </div>
   *
   * @param {Array<String>|Object} elements - A list of valid HTML elements or an object with one or
   *   more of the following properties:
   *   - **htmlElements** - `{Array<String>}` - A list of elements to extend the current list of
   *     HTML elements.
   *   - **htmlVoidElements** - `{Array<String>}` - A list of elements to extend the current list of
   *     void HTML elements; i.e. elements that do not have an end tag.
   *   - **svgElements** - `{Array<String>}` - A list of elements to extend the current list of SVG
   *     elements. The list of SVG elements is only taken into account if SVG is
   *     {@link ngSanitize.$sanitizeProvider#enableSvg enabled} for `$sanitize`.
   *
   * Passing an array (`[...]`) is equivalent to passing `{htmlElements: [...]}`.
   *
   * @return {$sanitizeProvider} Returns self for chaining.
   */
  this.addValidElements = function(elements) {
    if (!hasBeenInstantiated) {
      if (isArray(elements)) {
        elements = {htmlElements: elements};
      }

      addElementsTo(svgElements, elements.svgElements);
      addElementsTo(voidElements, elements.htmlVoidElements);
      addElementsTo(validElements, elements.htmlVoidElements);
      addElementsTo(validElements, elements.htmlElements);
    }

    return this;
  };


  /**
   * @ngdoc method
   * @name $sanitizeProvider#addValidAttrs
   * @kind function
   *
   * @description
   * Extends the built-in list of valid attributes, i.e. attributes that are considered safe and are
   * not stripped off during sanitization.
   *
   * **Note**:
   * The new attributes will not be treated as URI attributes, which means their values will not be
   * sanitized as URIs using `$compileProvider`'s
   * {@link ng.$compileProvider#aHrefSanitizationWhitelist aHrefSanitizationWhitelist} and
   * {@link ng.$compileProvider#imgSrcSanitizationWhitelist imgSrcSanitizationWhitelist}.
   *
   * <div class="alert alert-info">
   *   This method must be called during the {@link angular.Module#config config} phase. Once the
   *   `$sanitize` service has been instantiated, this method has no effect.
   * </div>
   *
   * <div class="alert alert-warning">
   *   Keep in mind that extending the built-in list of attributes may expose your app to XSS or
   *   other vulnerabilities. Be very mindful of the attributes you add.
   * </div>
   *
   * @param {Array<String>} attrs - A list of valid attributes.
   *
   * @returns {$sanitizeProvider} Returns self for chaining.
   */
  this.addValidAttrs = function(attrs) {
    if (!hasBeenInstantiated) {
      extend(validAttrs, arrayToMap(attrs, true));
    }
    return this;
  };

  //////////////////////////////////////////////////////////////////////////////////////////////////
  // Private stuff
  //////////////////////////////////////////////////////////////////////////////////////////////////

  bind = angular.bind;
  extend = angular.extend;
  forEach = angular.forEach;
  isArray = angular.isArray;
  isDefined = angular.isDefined;
  lowercase = angular.lowercase;
  noop = angular.noop;

  htmlParser = htmlParserImpl;
  htmlSanitizeWriter = htmlSanitizeWriterImpl;

  nodeContains = window.Node.prototype.contains || /** @this */ function(arg) {
    // eslint-disable-next-line no-bitwise
    return !!(this.compareDocumentPosition(arg) & 16);
  };

  // Regular Expressions for parsing tags and attributes
  var SURROGATE_PAIR_REGEXP = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g,
    // Match everything outside of normal chars and " (quote character)
    NON_ALPHANUMERIC_REGEXP = /([^#-~ |!])/g;


  // Good source of info about elements and attributes
  // http://dev.w3.org/html5/spec/Overview.html#semantics
  // http://simon.html5.org/html-elements

  // Safe Void Elements - HTML5
  // http://dev.w3.org/html5/spec/Overview.html#void-elements
  var voidElements = stringToMap('area,br,col,hr,img,wbr');

  // Elements that you can, intentionally, leave open (and which close themselves)
  // http://dev.w3.org/html5/spec/Overview.html#optional-tags
  var optionalEndTagBlockElements = stringToMap('colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr'),
      optionalEndTagInlineElements = stringToMap('rp,rt'),
      optionalEndTagElements = extend({},
                                              optionalEndTagInlineElements,
                                              optionalEndTagBlockElements);

  // Safe Block Elements - HTML5
  var blockElements = extend({}, optionalEndTagBlockElements, stringToMap('address,article,' +
          'aside,blockquote,caption,center,del,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,' +
          'h6,header,hgroup,hr,ins,map,menu,nav,ol,pre,section,table,ul'));

  // Inline Elements - HTML5
  var inlineElements = extend({}, optionalEndTagInlineElements, stringToMap('a,abbr,acronym,b,' +
          'bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,q,ruby,rp,rt,s,' +
          'samp,small,span,strike,strong,sub,sup,time,tt,u,var'));

  // SVG Elements
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Elements
  // Note: the elements animate,animateColor,animateMotion,animateTransform,set are intentionally omitted.
  // They can potentially allow for arbitrary javascript to be executed. See #11290
  var svgElements = stringToMap('circle,defs,desc,ellipse,font-face,font-face-name,font-face-src,g,glyph,' +
          'hkern,image,linearGradient,line,marker,metadata,missing-glyph,mpath,path,polygon,polyline,' +
          'radialGradient,rect,stop,svg,switch,text,title,tspan');

  // Blocked Elements (will be stripped)
  var blockedElements = stringToMap('script,style');

  var validElements = extend({},
                                     voidElements,
                                     blockElements,
                                     inlineElements,
                                     optionalEndTagElements);

  //Attributes that have href and hence need to be sanitized
  var uriAttrs = stringToMap('background,cite,href,longdesc,src,xlink:href,xml:base');

  var htmlAttrs = stringToMap('abbr,align,alt,axis,bgcolor,border,cellpadding,cellspacing,class,clear,' +
      'color,cols,colspan,compact,coords,dir,face,headers,height,hreflang,hspace,' +
      'ismap,lang,language,nohref,nowrap,rel,rev,rows,rowspan,rules,' +
      'scope,scrolling,shape,size,span,start,summary,tabindex,target,title,type,' +
      'valign,value,vspace,width');

  // SVG attributes (without "id" and "name" attributes)
  // https://wiki.whatwg.org/wiki/Sanitization_rules#svg_Attributes
  var svgAttrs = stringToMap('accent-height,accumulate,additive,alphabetic,arabic-form,ascent,' +
      'baseProfile,bbox,begin,by,calcMode,cap-height,class,color,color-rendering,content,' +
      'cx,cy,d,dx,dy,descent,display,dur,end,fill,fill-rule,font-family,font-size,font-stretch,' +
      'font-style,font-variant,font-weight,from,fx,fy,g1,g2,glyph-name,gradientUnits,hanging,' +
      'height,horiz-adv-x,horiz-origin-x,ideographic,k,keyPoints,keySplines,keyTimes,lang,' +
      'marker-end,marker-mid,marker-start,markerHeight,markerUnits,markerWidth,mathematical,' +
      'max,min,offset,opacity,orient,origin,overline-position,overline-thickness,panose-1,' +
      'path,pathLength,points,preserveAspectRatio,r,refX,refY,repeatCount,repeatDur,' +
      'requiredExtensions,requiredFeatures,restart,rotate,rx,ry,slope,stemh,stemv,stop-color,' +
      'stop-opacity,strikethrough-position,strikethrough-thickness,stroke,stroke-dasharray,' +
      'stroke-dashoffset,stroke-linecap,stroke-linejoin,stroke-miterlimit,stroke-opacity,' +
      'stroke-width,systemLanguage,target,text-anchor,to,transform,type,u1,u2,underline-position,' +
      'underline-thickness,unicode,unicode-range,units-per-em,values,version,viewBox,visibility,' +
      'width,widths,x,x-height,x1,x2,xlink:actuate,xlink:arcrole,xlink:role,xlink:show,xlink:title,' +
      'xlink:type,xml:base,xml:lang,xml:space,xmlns,xmlns:xlink,y,y1,y2,zoomAndPan', true);

  var validAttrs = extend({},
                                  uriAttrs,
                                  svgAttrs,
                                  htmlAttrs);

  function stringToMap(str, lowercaseKeys) {
    return arrayToMap(str.split(','), lowercaseKeys);
  }

  function arrayToMap(items, lowercaseKeys) {
    var obj = {}, i;
    for (i = 0; i < items.length; i++) {
      obj[lowercaseKeys ? lowercase(items[i]) : items[i]] = true;
    }
    return obj;
  }

  function addElementsTo(elementsMap, newElements) {
    if (newElements && newElements.length) {
      extend(elementsMap, arrayToMap(newElements));
    }
  }

  /**
   * Create an inert document that contains the dirty HTML that needs sanitizing
   * Depending upon browser support we use one of three strategies for doing this.
   * Support: Safari 10.x -> XHR strategy
   * Support: Firefox -> DomParser strategy
   */
  var getInertBodyElement /* function(html: string): HTMLBodyElement */ = (function(window, document) {
    var inertDocument;
    if (document && document.implementation) {
      inertDocument = document.implementation.createHTMLDocument('inert');
    } else {
      throw $sanitizeMinErr('noinert', 'Can\'t create an inert html document');
    }
    var inertBodyElement = (inertDocument.documentElement || inertDocument.getDocumentElement()).querySelector('body');

    // Check for the Safari 10.1 bug - which allows JS to run inside the SVG G element
    inertBodyElement.innerHTML = '<svg><g onload="this.parentNode.remove()"></g></svg>';
    if (!inertBodyElement.querySelector('svg')) {
      return getInertBodyElement_XHR;
    } else {
      // Check for the Firefox bug - which prevents the inner img JS from being sanitized
      inertBodyElement.innerHTML = '<svg><p><style><img src="</style><img src=x onerror=alert(1)//">';
      if (inertBodyElement.querySelector('svg img')) {
        return getInertBodyElement_DOMParser;
      } else {
        return getInertBodyElement_InertDocument;
      }
    }

    function getInertBodyElement_XHR(html) {
      // We add this dummy element to ensure that the rest of the content is parsed as expected
      // e.g. leading whitespace is maintained and tags like `<meta>` do not get hoisted to the `<head>` tag.
      html = '<remove></remove>' + html;
      try {
        html = encodeURI(html);
      } catch (e) {
        return undefined;
      }
      var xhr = new window.XMLHttpRequest();
      xhr.responseType = 'document';
      xhr.open('GET', 'data:text/html;charset=utf-8,' + html, false);
      xhr.send(null);
      var body = xhr.response.body;
      body.firstChild.remove();
      return body;
    }

    function getInertBodyElement_DOMParser(html) {
      // We add this dummy element to ensure that the rest of the content is parsed as expected
      // e.g. leading whitespace is maintained and tags like `<meta>` do not get hoisted to the `<head>` tag.
      html = '<remove></remove>' + html;
      try {
        var body = new window.DOMParser().parseFromString(html, 'text/html').body;
        body.firstChild.remove();
        return body;
      } catch (e) {
        return undefined;
      }
    }

    function getInertBodyElement_InertDocument(html) {
      inertBodyElement.innerHTML = html;

      // Support: IE 9-11 only
      // strip custom-namespaced attributes on IE<=11
      if (document.documentMode) {
        stripCustomNsAttrs(inertBodyElement);
      }

      return inertBodyElement;
    }
  })(window, window.document);

  /**
   * @example
   * htmlParser(htmlString, {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * });
   *
   * @param {string} html string
   * @param {object} handler
   */
  function htmlParserImpl(html, handler) {
    if (html === null || html === undefined) {
      html = '';
    } else if (typeof html !== 'string') {
      html = '' + html;
    }

    var inertBodyElement = getInertBodyElement(html);
    if (!inertBodyElement) return '';

    //mXSS protection
    var mXSSAttempts = 5;
    do {
      if (mXSSAttempts === 0) {
        throw $sanitizeMinErr('uinput', 'Failed to sanitize html because the input is unstable');
      }
      mXSSAttempts--;

      // trigger mXSS if it is going to happen by reading and writing the innerHTML
      html = inertBodyElement.innerHTML;
      inertBodyElement = getInertBodyElement(html);
    } while (html !== inertBodyElement.innerHTML);

    var node = inertBodyElement.firstChild;
    while (node) {
      switch (node.nodeType) {
        case 1: // ELEMENT_NODE
          handler.start(node.nodeName.toLowerCase(), attrToMap(node.attributes));
          break;
        case 3: // TEXT NODE
          handler.chars(node.textContent);
          break;
      }

      var nextNode;
      if (!(nextNode = node.firstChild)) {
        if (node.nodeType === 1) {
          handler.end(node.nodeName.toLowerCase());
        }
        nextNode = getNonDescendant('nextSibling', node);
        if (!nextNode) {
          while (nextNode == null) {
            node = getNonDescendant('parentNode', node);
            if (node === inertBodyElement) break;
            nextNode = getNonDescendant('nextSibling', node);
            if (node.nodeType === 1) {
              handler.end(node.nodeName.toLowerCase());
            }
          }
        }
      }
      node = nextNode;
    }

    while ((node = inertBodyElement.firstChild)) {
      inertBodyElement.removeChild(node);
    }
  }

  function attrToMap(attrs) {
    var map = {};
    for (var i = 0, ii = attrs.length; i < ii; i++) {
      var attr = attrs[i];
      map[attr.name] = attr.value;
    }
    return map;
  }


  /**
   * Escapes all potentially dangerous characters, so that the
   * resulting string can be safely inserted into attribute or
   * element text.
   * @param value
   * @returns {string} escaped text
   */
  function encodeEntities(value) {
    return value.
      replace(/&/g, '&amp;').
      replace(SURROGATE_PAIR_REGEXP, function(value) {
        var hi = value.charCodeAt(0);
        var low = value.charCodeAt(1);
        return '&#' + (((hi - 0xD800) * 0x400) + (low - 0xDC00) + 0x10000) + ';';
      }).
      replace(NON_ALPHANUMERIC_REGEXP, function(value) {
        return '&#' + value.charCodeAt(0) + ';';
      }).
      replace(/</g, '&lt;').
      replace(/>/g, '&gt;');
  }

  /**
   * create an HTML/XML writer which writes to buffer
   * @param {Array} buf use buf.join('') to get out sanitized html string
   * @returns {object} in the form of {
   *     start: function(tag, attrs) {},
   *     end: function(tag) {},
   *     chars: function(text) {},
   *     comment: function(text) {}
   * }
   */
  function htmlSanitizeWriterImpl(buf, uriValidator) {
    var ignoreCurrentElement = false;
    var out = bind(buf, buf.push);
    return {
      start: function(tag, attrs) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && blockedElements[tag]) {
          ignoreCurrentElement = tag;
        }
        if (!ignoreCurrentElement && validElements[tag] === true) {
          out('<');
          out(tag);
          forEach(attrs, function(value, key) {
            var lkey = lowercase(key);
            var isImage = (tag === 'img' && lkey === 'src') || (lkey === 'background');
            if (validAttrs[lkey] === true &&
              (uriAttrs[lkey] !== true || uriValidator(value, isImage))) {
              out(' ');
              out(key);
              out('="');
              out(encodeEntities(value));
              out('"');
            }
          });
          out('>');
        }
      },
      end: function(tag) {
        tag = lowercase(tag);
        if (!ignoreCurrentElement && validElements[tag] === true && voidElements[tag] !== true) {
          out('</');
          out(tag);
          out('>');
        }
        // eslint-disable-next-line eqeqeq
        if (tag == ignoreCurrentElement) {
          ignoreCurrentElement = false;
        }
      },
      chars: function(chars) {
        if (!ignoreCurrentElement) {
          out(encodeEntities(chars));
        }
      }
    };
  }


  /**
   * When IE9-11 comes across an unknown namespaced attribute e.g. 'xlink:foo' it adds 'xmlns:ns1' attribute to declare
   * ns1 namespace and prefixes the attribute with 'ns1' (e.g. 'ns1:xlink:foo'). This is undesirable since we don't want
   * to allow any of these custom attributes. This method strips them all.
   *
   * @param node Root element to process
   */
  function stripCustomNsAttrs(node) {
    while (node) {
      if (node.nodeType === window.Node.ELEMENT_NODE) {
        var attrs = node.attributes;
        for (var i = 0, l = attrs.length; i < l; i++) {
          var attrNode = attrs[i];
          var attrName = attrNode.name.toLowerCase();
          if (attrName === 'xmlns:ns1' || attrName.lastIndexOf('ns1:', 0) === 0) {
            node.removeAttributeNode(attrNode);
            i--;
            l--;
          }
        }
      }

      var nextNode = node.firstChild;
      if (nextNode) {
        stripCustomNsAttrs(nextNode);
      }

      node = getNonDescendant('nextSibling', node);
    }
  }

  function getNonDescendant(propName, node) {
    // An element is clobbered if its `propName` property points to one of its descendants
    var nextNode = node[propName];
    if (nextNode && nodeContains.call(node, nextNode)) {
      throw $sanitizeMinErr('elclob', 'Failed to sanitize html because the element is clobbered: {0}', node.outerHTML || node.outerText);
    }
    return nextNode;
  }
}

function sanitizeText(chars) {
  var buf = [];
  var writer = htmlSanitizeWriter(buf, noop);
  writer.chars(chars);
  return buf.join('');
}


// define ngSanitize module and register $sanitize service
angular.module('ngSanitize', [])
  .provider('$sanitize', $SanitizeProvider)
  .info({ angularVersion: '1.6.10' });

/**
 * @ngdoc filter
 * @name linky
 * @kind function
 *
 * @description
 * Finds links in text input and turns them into html links. Supports `http/https/ftp/sftp/mailto` and
 * plain email address links.
 *
 * Requires the {@link ngSanitize `ngSanitize`} module to be installed.
 *
 * @param {string} text Input text.
 * @param {string} [target] Window (`_blank|_self|_parent|_top`) or named frame to open links in.
 * @param {object|function(url)} [attributes] Add custom attributes to the link element.
 *
 *    Can be one of:
 *
 *    - `object`: A map of attributes
 *    - `function`: Takes the url as a parameter and returns a map of attributes
 *
 *    If the map of attributes contains a value for `target`, it overrides the value of
 *    the target parameter.
 *
 *
 * @returns {string} Html-linkified and {@link $sanitize sanitized} text.
 *
 * @usage
   <span ng-bind-html="linky_expression | linky"></span>
 *
 * @example
   <example module="linkyExample" deps="angular-sanitize.js" name="linky-filter">
     <file name="index.html">
       <div ng-controller="ExampleController">
       Snippet: <textarea ng-model="snippet" cols="60" rows="3"></textarea>
       <table>
         <tr>
           <th>Filter</th>
           <th>Source</th>
           <th>Rendered</th>
         </tr>
         <tr id="linky-filter">
           <td>linky filter</td>
           <td>
             <pre>&lt;div ng-bind-html="snippet | linky"&gt;<br>&lt;/div&gt;</pre>
           </td>
           <td>
             <div ng-bind-html="snippet | linky"></div>
           </td>
         </tr>
         <tr id="linky-target">
          <td>linky target</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_blank'"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_blank'"></div>
          </td>
         </tr>
         <tr id="linky-custom-attributes">
          <td>linky custom attributes</td>
          <td>
            <pre>&lt;div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"&gt;<br>&lt;/div&gt;</pre>
          </td>
          <td>
            <div ng-bind-html="snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}"></div>
          </td>
         </tr>
         <tr id="escaped-html">
           <td>no filter</td>
           <td><pre>&lt;div ng-bind="snippet"&gt;<br>&lt;/div&gt;</pre></td>
           <td><div ng-bind="snippet"></div></td>
         </tr>
       </table>
     </file>
     <file name="script.js">
       angular.module('linkyExample', ['ngSanitize'])
         .controller('ExampleController', ['$scope', function($scope) {
           $scope.snippet =
             'Pretty text with some links:\n' +
             'http://angularjs.org/,\n' +
             'mailto:us@somewhere.org,\n' +
             'another@somewhere.org,\n' +
             'and one more: ftp://127.0.0.1/.';
           $scope.snippetWithSingleURL = 'http://angularjs.org/';
         }]);
     </file>
     <file name="protractor.js" type="protractor">
       it('should linkify the snippet with urls', function() {
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(4);
       });

       it('should not linkify snippet without the linky filter', function() {
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText()).
             toBe('Pretty text with some links: http://angularjs.org/, mailto:us@somewhere.org, ' +
                  'another@somewhere.org, and one more: ftp://127.0.0.1/.');
         expect(element.all(by.css('#escaped-html a')).count()).toEqual(0);
       });

       it('should update', function() {
         element(by.model('snippet')).clear();
         element(by.model('snippet')).sendKeys('new http://link.');
         expect(element(by.id('linky-filter')).element(by.binding('snippet | linky')).getText()).
             toBe('new http://link.');
         expect(element.all(by.css('#linky-filter a')).count()).toEqual(1);
         expect(element(by.id('escaped-html')).element(by.binding('snippet')).getText())
             .toBe('new http://link.');
       });

       it('should work with the target property', function() {
        expect(element(by.id('linky-target')).
            element(by.binding("snippetWithSingleURL | linky:'_blank'")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-target a')).getAttribute('target')).toEqual('_blank');
       });

       it('should optionally add custom attributes', function() {
        expect(element(by.id('linky-custom-attributes')).
            element(by.binding("snippetWithSingleURL | linky:'_self':{rel: 'nofollow'}")).getText()).
            toBe('http://angularjs.org/');
        expect(element(by.css('#linky-custom-attributes a')).getAttribute('rel')).toEqual('nofollow');
       });
     </file>
   </example>
 */
angular.module('ngSanitize').filter('linky', ['$sanitize', function($sanitize) {
  var LINKY_URL_REGEXP =
        /((s?ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/i,
      MAILTO_REGEXP = /^mailto:/i;

  var linkyMinErr = angular.$$minErr('linky');
  var isDefined = angular.isDefined;
  var isFunction = angular.isFunction;
  var isObject = angular.isObject;
  var isString = angular.isString;

  return function(text, target, attributes) {
    if (text == null || text === '') return text;
    if (!isString(text)) throw linkyMinErr('notstring', 'Expected string but received: {0}', text);

    var attributesFn =
      isFunction(attributes) ? attributes :
      isObject(attributes) ? function getAttributesObject() {return attributes;} :
      function getEmptyAttributesObject() {return {};};

    var match;
    var raw = text;
    var html = [];
    var url;
    var i;
    while ((match = raw.match(LINKY_URL_REGEXP))) {
      // We can not end in these as they are sometimes found at the end of the sentence
      url = match[0];
      // if we did not match ftp/http/www/mailto then assume mailto
      if (!match[2] && !match[4]) {
        url = (match[3] ? 'http://' : 'mailto:') + url;
      }
      i = match.index;
      addText(raw.substr(0, i));
      addLink(url, match[0].replace(MAILTO_REGEXP, ''));
      raw = raw.substring(i + match[0].length);
    }
    addText(raw);
    return $sanitize(html.join(''));

    function addText(text) {
      if (!text) {
        return;
      }
      html.push(sanitizeText(text));
    }

    function addLink(url, text) {
      var key, linkAttributes = attributesFn(url);
      html.push('<a ');

      for (key in linkAttributes) {
        html.push(key + '="' + linkAttributes[key] + '" ');
      }

      if (isDefined(target) && !('target' in linkAttributes)) {
        html.push('target="',
                  target,
                  '" ');
      }
      html.push('href="',
                url.replace(/"/g, '&quot;'),
                '">');
      addText(text);
      html.push('</a>');
    }
  };
}]);


})(window, window.angular);

/**
 * marked - a markdown parser
 * Copyright (c) 2011-2014, Christopher Jeffrey. (MIT Licensed)
 * https://github.com/markedjs/marked
 */

;(function(root) {
'use strict';

/**
 * Block-Level Grammar
 */

var block = {
  newline: /^\n+/,
  code: /^( {4}[^\n]+\n*)+/,
  fences: noop,
  hr: /^ {0,3}((?:- *){3,}|(?:_ *){3,}|(?:\* *){3,})(?:\n+|$)/,
  heading: /^ *(#{1,6}) *([^\n]+?) *#* *(?:\n+|$)/,
  nptable: noop,
  blockquote: /^( {0,3}> ?(paragraph|[^\n]*)(?:\n|$))+/,
  list: /^( *)(bull) [\s\S]+?(?:hr|def|\n{2,}(?! )(?!\1bull )\n*|\s*$)/,
  html: /^ *(?:comment *(?:\n|\s*$)|closed *(?:\n{2,}|\s*$)|closing *(?:\n{2,}|\s*$))/,
  def: /^ {0,3}\[(label)\]: *\n? *<?([^\s>]+)>?(?:(?: +\n? *| *\n *)(title))? *(?:\n+|$)/,
  table: noop,
  lheading: /^([^\n]+)\n *(=|-){2,} *(?:\n+|$)/,
  paragraph: /^([^\n]+(?:\n?(?!hr|heading|lheading| {0,3}>|tag)[^\n]+)+)/,
  text: /^[^\n]+/
};

block._label = /(?:\\[\[\]]|[^\[\]])+/;
block._title = /(?:"(?:\\"|[^"]|"[^"\n]*")*"|'\n?(?:[^'\n]+\n?)*'|\([^()]*\))/;
block.def = edit(block.def)
  .replace('label', block._label)
  .replace('title', block._title)
  .getRegex();

block.bullet = /(?:[*+-]|\d+\.)/;
block.item = /^( *)(bull) [^\n]*(?:\n(?!\1bull )[^\n]*)*/;
block.item = edit(block.item, 'gm')
  .replace(/bull/g, block.bullet)
  .getRegex();

block.list = edit(block.list)
  .replace(/bull/g, block.bullet)
  .replace('hr', '\\n+(?=\\1?(?:(?:- *){3,}|(?:_ *){3,}|(?:\\* *){3,})(?:\\n+|$))')
  .replace('def', '\\n+(?=' + block.def.source + ')')
  .getRegex();

block._tag = '(?!(?:'
  + 'a|em|strong|small|s|cite|q|dfn|abbr|data|time|code'
  + '|var|samp|kbd|sub|sup|i|b|u|mark|ruby|rt|rp|bdi|bdo'
  + '|span|br|wbr|ins|del|img)\\b)\\w+(?!:|[^\\w\\s@]*@)\\b';

block.html = edit(block.html)
  .replace('comment', /<!--[\s\S]*?-->/)
  .replace('closed', /<(tag)[\s\S]+?<\/\1>/)
  .replace('closing', /<tag(?:"[^"]*"|'[^']*'|\s[^'"\/>\s]*)*?\/?>/)
  .replace(/tag/g, block._tag)
  .getRegex();

block.paragraph = edit(block.paragraph)
  .replace('hr', block.hr)
  .replace('heading', block.heading)
  .replace('lheading', block.lheading)
  .replace('tag', '<' + block._tag)
  .getRegex();

block.blockquote = edit(block.blockquote)
  .replace('paragraph', block.paragraph)
  .getRegex();

/**
 * Normal Block Grammar
 */

block.normal = merge({}, block);

/**
 * GFM Block Grammar
 */

block.gfm = merge({}, block.normal, {
  fences: /^ *(`{3,}|~{3,})[ \.]*(\S+)? *\n([\s\S]*?)\n? *\1 *(?:\n+|$)/,
  paragraph: /^/,
  heading: /^ *(#{1,6}) +([^\n]+?) *#* *(?:\n+|$)/
});

block.gfm.paragraph = edit(block.paragraph)
  .replace('(?!', '(?!'
    + block.gfm.fences.source.replace('\\1', '\\2') + '|'
    + block.list.source.replace('\\1', '\\3') + '|')
  .getRegex();

/**
 * GFM + Tables Block Grammar
 */

block.tables = merge({}, block.gfm, {
  nptable: /^ *(\S.*\|.*)\n *([-:]+ *\|[-| :]*)\n((?:.*\|.*(?:\n|$))*)\n*/,
  table: /^ *\|(.+)\n *\|( *[-:]+[-| :]*)\n((?: *\|.*(?:\n|$))*)\n*/
});

/**
 * Block Lexer
 */

function Lexer(options) {
  this.tokens = [];
  this.tokens.links = {};
  this.options = options || marked.defaults;
  this.rules = block.normal;

  if (this.options.gfm) {
    if (this.options.tables) {
      this.rules = block.tables;
    } else {
      this.rules = block.gfm;
    }
  }
}

/**
 * Expose Block Rules
 */

Lexer.rules = block;

/**
 * Static Lex Method
 */

Lexer.lex = function(src, options) {
  var lexer = new Lexer(options);
  return lexer.lex(src);
};

/**
 * Preprocessing
 */

Lexer.prototype.lex = function(src) {
  src = src
    .replace(/\r\n|\r/g, '\n')
    .replace(/\t/g, '    ')
    .replace(/\u00a0/g, ' ')
    .replace(/\u2424/g, '\n');

  return this.token(src, true);
};

/**
 * Lexing
 */

Lexer.prototype.token = function(src, top) {
  src = src.replace(/^ +$/gm, '');
  var next,
      loose,
      cap,
      bull,
      b,
      item,
      space,
      i,
      tag,
      l,
      isordered;

  while (src) {
    // newline
    if (cap = this.rules.newline.exec(src)) {
      src = src.substring(cap[0].length);
      if (cap[0].length > 1) {
        this.tokens.push({
          type: 'space'
        });
      }
    }

    // code
    if (cap = this.rules.code.exec(src)) {
      src = src.substring(cap[0].length);
      cap = cap[0].replace(/^ {4}/gm, '');
      this.tokens.push({
        type: 'code',
        text: !this.options.pedantic
          ? cap.replace(/\n+$/, '')
          : cap
      });
      continue;
    }

    // fences (gfm)
    if (cap = this.rules.fences.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'code',
        lang: cap[2],
        text: cap[3] || ''
      });
      continue;
    }

    // heading
    if (cap = this.rules.heading.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'heading',
        depth: cap[1].length,
        text: cap[2]
      });
      continue;
    }

    // table no leading pipe (gfm)
    if (top && (cap = this.rules.nptable.exec(src))) {
      src = src.substring(cap[0].length);

      item = {
        type: 'table',
        header: cap[1].replace(/^ *| *\| *$/g, '').split(/ *\| */),
        align: cap[2].replace(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/\n$/, '').split('\n')
      };

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right';
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center';
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left';
        } else {
          item.align[i] = null;
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = item.cells[i].split(/ *\| */);
      }

      this.tokens.push(item);

      continue;
    }

    // hr
    if (cap = this.rules.hr.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'hr'
      });
      continue;
    }

    // blockquote
    if (cap = this.rules.blockquote.exec(src)) {
      src = src.substring(cap[0].length);

      this.tokens.push({
        type: 'blockquote_start'
      });

      cap = cap[0].replace(/^ *> ?/gm, '');

      // Pass `top` to keep the current
      // "toplevel" state. This is exactly
      // how markdown.pl works.
      this.token(cap, top);

      this.tokens.push({
        type: 'blockquote_end'
      });

      continue;
    }

    // list
    if (cap = this.rules.list.exec(src)) {
      src = src.substring(cap[0].length);
      bull = cap[2];
      isordered = bull.length > 1;

      this.tokens.push({
        type: 'list_start',
        ordered: isordered,
        start: isordered ? +bull : ''
      });

      // Get each top-level item.
      cap = cap[0].match(this.rules.item);

      next = false;
      l = cap.length;
      i = 0;

      for (; i < l; i++) {
        item = cap[i];

        // Remove the list item's bullet
        // so it is seen as the next token.
        space = item.length;
        item = item.replace(/^ *([*+-]|\d+\.) +/, '');

        // Outdent whatever the
        // list item contains. Hacky.
        if (~item.indexOf('\n ')) {
          space -= item.length;
          item = !this.options.pedantic
            ? item.replace(new RegExp('^ {1,' + space + '}', 'gm'), '')
            : item.replace(/^ {1,4}/gm, '');
        }

        // Determine whether the next list item belongs here.
        // Backpedal if it does not belong in this list.
        if (this.options.smartLists && i !== l - 1) {
          b = block.bullet.exec(cap[i + 1])[0];
          if (bull !== b && !(bull.length > 1 && b.length > 1)) {
            src = cap.slice(i + 1).join('\n') + src;
            i = l - 1;
          }
        }

        // Determine whether item is loose or not.
        // Use: /(^|\n)(?! )[^\n]+\n\n(?!\s*$)/
        // for discount behavior.
        loose = next || /\n\n(?!\s*$)/.test(item);
        if (i !== l - 1) {
          next = item.charAt(item.length - 1) === '\n';
          if (!loose) loose = next;
        }

        this.tokens.push({
          type: loose
            ? 'loose_item_start'
            : 'list_item_start'
        });

        // Recurse.
        this.token(item, false);

        this.tokens.push({
          type: 'list_item_end'
        });
      }

      this.tokens.push({
        type: 'list_end'
      });

      continue;
    }

    // html
    if (cap = this.rules.html.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: this.options.sanitize
          ? 'paragraph'
          : 'html',
        pre: !this.options.sanitizer
          && (cap[1] === 'pre' || cap[1] === 'script' || cap[1] === 'style'),
        text: cap[0]
      });
      continue;
    }

    // def
    if (top && (cap = this.rules.def.exec(src))) {
      src = src.substring(cap[0].length);
      if (cap[3]) cap[3] = cap[3].substring(1, cap[3].length - 1);
      tag = cap[1].toLowerCase();
      if (!this.tokens.links[tag]) {
        this.tokens.links[tag] = {
          href: cap[2],
          title: cap[3]
        };
      }
      continue;
    }

    // table (gfm)
    if (top && (cap = this.rules.table.exec(src))) {
      src = src.substring(cap[0].length);

      item = {
        type: 'table',
        header: cap[1].replace(/^ *| *\| *$/g, '').split(/ *\| */),
        align: cap[2].replace(/^ *|\| *$/g, '').split(/ *\| */),
        cells: cap[3].replace(/(?: *\| *)?\n$/, '').split('\n')
      };

      for (i = 0; i < item.align.length; i++) {
        if (/^ *-+: *$/.test(item.align[i])) {
          item.align[i] = 'right';
        } else if (/^ *:-+: *$/.test(item.align[i])) {
          item.align[i] = 'center';
        } else if (/^ *:-+ *$/.test(item.align[i])) {
          item.align[i] = 'left';
        } else {
          item.align[i] = null;
        }
      }

      for (i = 0; i < item.cells.length; i++) {
        item.cells[i] = item.cells[i]
          .replace(/^ *\| *| *\| *$/g, '')
          .split(/ *\| */);
      }

      this.tokens.push(item);

      continue;
    }

    // lheading
    if (cap = this.rules.lheading.exec(src)) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'heading',
        depth: cap[2] === '=' ? 1 : 2,
        text: cap[1]
      });
      continue;
    }

    // top-level paragraph
    if (top && (cap = this.rules.paragraph.exec(src))) {
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'paragraph',
        text: cap[1].charAt(cap[1].length - 1) === '\n'
          ? cap[1].slice(0, -1)
          : cap[1]
      });
      continue;
    }

    // text
    if (cap = this.rules.text.exec(src)) {
      // Top-level should never reach here.
      src = src.substring(cap[0].length);
      this.tokens.push({
        type: 'text',
        text: cap[0]
      });
      continue;
    }

    if (src) {
      throw new Error('Infinite loop on byte: ' + src.charCodeAt(0));
    }
  }

  return this.tokens;
};

/**
 * Inline-Level Grammar
 */

var inline = {
  escape: /^\\([\\`*{}\[\]()#+\-.!_>])/,
  autolink: /^<(scheme:[^\s\x00-\x1f<>]*|email)>/,
  url: noop,
  tag: /^<!--[\s\S]*?-->|^<\/?[a-zA-Z0-9\-]+(?:"[^"]*"|'[^']*'|\s[^<'">\/\s]*)*?\/?>/,
  link: /^!?\[(inside)\]\(href\)/,
  reflink: /^!?\[(inside)\]\s*\[([^\]]*)\]/,
  nolink: /^!?\[((?:\[[^\[\]]*\]|\\[\[\]]|[^\[\]])*)\]/,
  strong: /^__([\s\S]+?)__(?!_)|^\*\*([\s\S]+?)\*\*(?!\*)/,
  em: /^_([^\s_](?:[^_]|__)+?[^\s_])_\b|^\*((?:\*\*|[^*])+?)\*(?!\*)/,
  code: /^(`+)\s*([\s\S]*?[^`]?)\s*\1(?!`)/,
  br: /^ {2,}\n(?!\s*$)/,
  del: noop,
  text: /^[\s\S]+?(?=[\\<!\[`*]|\b_| {2,}\n|$)/
};

inline._scheme = /[a-zA-Z][a-zA-Z0-9+.-]{1,31}/;
inline._email = /[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+(@)[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+(?![-_])/;

inline.autolink = edit(inline.autolink)
  .replace('scheme', inline._scheme)
  .replace('email', inline._email)
  .getRegex()

inline._inside = /(?:\[[^\[\]]*\]|\\[\[\]]|[^\[\]]|\](?=[^\[]*\]))*/;
inline._href = /\s*<?([\s\S]*?)>?(?:\s+['"]([\s\S]*?)['"])?\s*/;

inline.link = edit(inline.link)
  .replace('inside', inline._inside)
  .replace('href', inline._href)
  .getRegex();

inline.reflink = edit(inline.reflink)
  .replace('inside', inline._inside)
  .getRegex();

/**
 * Normal Inline Grammar
 */

inline.normal = merge({}, inline);

/**
 * Pedantic Inline Grammar
 */

inline.pedantic = merge({}, inline.normal, {
  strong: /^__(?=\S)([\s\S]*?\S)__(?!_)|^\*\*(?=\S)([\s\S]*?\S)\*\*(?!\*)/,
  em: /^_(?=\S)([\s\S]*?\S)_(?!_)|^\*(?=\S)([\s\S]*?\S)\*(?!\*)/
});

/**
 * GFM Inline Grammar
 */

inline.gfm = merge({}, inline.normal, {
  escape: edit(inline.escape).replace('])', '~|])').getRegex(),
  url: edit(/^((?:ftp|https?):\/\/|www\.)(?:[a-zA-Z0-9\-]+\.?)+[^\s<]*|^email/)
    .replace('email', inline._email)
    .getRegex(),
  _backpedal: /(?:[^?!.,:;*_~()&]+|\([^)]*\)|&(?![a-zA-Z0-9]+;$)|[?!.,:;*_~)]+(?!$))+/,
  del: /^~~(?=\S)([\s\S]*?\S)~~/,
  text: edit(inline.text)
    .replace(']|', '~]|')
    .replace('|', '|https?://|ftp://|www\\.|[a-zA-Z0-9.!#$%&\'*+/=?^_`{\\|}~-]+@|')
    .getRegex()
});

/**
 * GFM + Line Breaks Inline Grammar
 */

inline.breaks = merge({}, inline.gfm, {
  br: edit(inline.br).replace('{2,}', '*').getRegex(),
  text: edit(inline.gfm.text).replace('{2,}', '*').getRegex()
});

/**
 * Inline Lexer & Compiler
 */

function InlineLexer(links, options) {
  this.options = options || marked.defaults;
  this.links = links;
  this.rules = inline.normal;
  this.renderer = this.options.renderer || new Renderer();
  this.renderer.options = this.options;

  if (!this.links) {
    throw new Error('Tokens array requires a `links` property.');
  }

  if (this.options.gfm) {
    if (this.options.breaks) {
      this.rules = inline.breaks;
    } else {
      this.rules = inline.gfm;
    }
  } else if (this.options.pedantic) {
    this.rules = inline.pedantic;
  }
}

/**
 * Expose Inline Rules
 */

InlineLexer.rules = inline;

/**
 * Static Lexing/Compiling Method
 */

InlineLexer.output = function(src, links, options) {
  var inline = new InlineLexer(links, options);
  return inline.output(src);
};

/**
 * Lexing/Compiling
 */

InlineLexer.prototype.output = function(src) {
  var out = '',
      link,
      text,
      href,
      cap;

  while (src) {
    // escape
    if (cap = this.rules.escape.exec(src)) {
      src = src.substring(cap[0].length);
      out += cap[1];
      continue;
    }

    // autolink
    if (cap = this.rules.autolink.exec(src)) {
      src = src.substring(cap[0].length);
      if (cap[2] === '@') {
        text = escape(this.mangle(cap[1]));
        href = 'mailto:' + text;
      } else {
        text = escape(cap[1]);
        href = text;
      }
      out += this.renderer.link(href, null, text);
      continue;
    }

    // url (gfm)
    if (!this.inLink && (cap = this.rules.url.exec(src))) {
      cap[0] = this.rules._backpedal.exec(cap[0])[0];
      src = src.substring(cap[0].length);
      if (cap[2] === '@') {
        text = escape(cap[0]);
        href = 'mailto:' + text;
      } else {
        text = escape(cap[0]);
        if (cap[1] === 'www.') {
          href = 'http://' + text;
        } else {
          href = text;
        }
      }
      out += this.renderer.link(href, null, text);
      continue;
    }

    // tag
    if (cap = this.rules.tag.exec(src)) {
      if (!this.inLink && /^<a /i.test(cap[0])) {
        this.inLink = true;
      } else if (this.inLink && /^<\/a>/i.test(cap[0])) {
        this.inLink = false;
      }
      src = src.substring(cap[0].length);
      out += this.options.sanitize
        ? this.options.sanitizer
          ? this.options.sanitizer(cap[0])
          : escape(cap[0])
        : cap[0]
      continue;
    }

    // link
    if (cap = this.rules.link.exec(src)) {
      src = src.substring(cap[0].length);
      this.inLink = true;
      out += this.outputLink(cap, {
        href: cap[2],
        title: cap[3]
      });
      this.inLink = false;
      continue;
    }

    // reflink, nolink
    if ((cap = this.rules.reflink.exec(src))
        || (cap = this.rules.nolink.exec(src))) {
      src = src.substring(cap[0].length);
      link = (cap[2] || cap[1]).replace(/\s+/g, ' ');
      link = this.links[link.toLowerCase()];
      if (!link || !link.href) {
        out += cap[0].charAt(0);
        src = cap[0].substring(1) + src;
        continue;
      }
      this.inLink = true;
      out += this.outputLink(cap, link);
      this.inLink = false;
      continue;
    }

    // strong
    if (cap = this.rules.strong.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.strong(this.output(cap[2] || cap[1]));
      continue;
    }

    // em
    if (cap = this.rules.em.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.em(this.output(cap[2] || cap[1]));
      continue;
    }

    // code
    if (cap = this.rules.code.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.codespan(escape(cap[2].trim(), true));
      continue;
    }

    // br
    if (cap = this.rules.br.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.br();
      continue;
    }

    // del (gfm)
    if (cap = this.rules.del.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.del(this.output(cap[1]));
      continue;
    }

    // text
    if (cap = this.rules.text.exec(src)) {
      src = src.substring(cap[0].length);
      out += this.renderer.text(escape(this.smartypants(cap[0])));
      continue;
    }

    if (src) {
      throw new Error('Infinite loop on byte: ' + src.charCodeAt(0));
    }
  }

  return out;
};

/**
 * Compile Link
 */

InlineLexer.prototype.outputLink = function(cap, link) {
  var href = escape(link.href),
      title = link.title ? escape(link.title) : null;

  return cap[0].charAt(0) !== '!'
    ? this.renderer.link(href, title, this.output(cap[1]))
    : this.renderer.image(href, title, escape(cap[1]));
};

/**
 * Smartypants Transformations
 */

InlineLexer.prototype.smartypants = function(text) {
  if (!this.options.smartypants) return text;
  return text
    // em-dashes
    .replace(/---/g, '\u2014')
    // en-dashes
    .replace(/--/g, '\u2013')
    // opening singles
    .replace(/(^|[-\u2014/(\[{"\s])'/g, '$1\u2018')
    // closing singles & apostrophes
    .replace(/'/g, '\u2019')
    // opening doubles
    .replace(/(^|[-\u2014/(\[{\u2018\s])"/g, '$1\u201c')
    // closing doubles
    .replace(/"/g, '\u201d')
    // ellipses
    .replace(/\.{3}/g, '\u2026');
};

/**
 * Mangle Links
 */

InlineLexer.prototype.mangle = function(text) {
  if (!this.options.mangle) return text;
  var out = '',
      l = text.length,
      i = 0,
      ch;

  for (; i < l; i++) {
    ch = text.charCodeAt(i);
    if (Math.random() > 0.5) {
      ch = 'x' + ch.toString(16);
    }
    out += '&#' + ch + ';';
  }

  return out;
};

/**
 * Renderer
 */

function Renderer(options) {
  this.options = options || {};
}

Renderer.prototype.code = function(code, lang, escaped) {
  if (this.options.highlight) {
    var out = this.options.highlight(code, lang);
    if (out != null && out !== code) {
      escaped = true;
      code = out;
    }
  }

  if (!lang) {
    return '<pre><code>'
      + (escaped ? code : escape(code, true))
      + '\n</code></pre>';
  }

  return '<pre><code class="'
    + this.options.langPrefix
    + escape(lang, true)
    + '">'
    + (escaped ? code : escape(code, true))
    + '\n</code></pre>\n';
};

Renderer.prototype.blockquote = function(quote) {
  return '<blockquote>\n' + quote + '</blockquote>\n';
};

Renderer.prototype.html = function(html) {
  return html;
};

Renderer.prototype.heading = function(text, level, raw) {
  return '<h'
    + level
    + ' id="'
    + this.options.headerPrefix
    + raw.toLowerCase().replace(/[^\w]+/g, '-')
    + '">'
    + text
    + '</h'
    + level
    + '>\n';
};

Renderer.prototype.hr = function() {
  return this.options.xhtml ? '<hr/>\n' : '<hr>\n';
};

Renderer.prototype.list = function(body, ordered, start) {
  var type = ordered ? 'ol' : 'ul',
      startatt = (ordered && start !== 1) ? (' start="' + start + '"') : '';
  return '<' + type + startatt + '>\n' + body + '</' + type + '>\n';
};

Renderer.prototype.listitem = function(text) {
  return '<li>' + text + '</li>\n';
};

Renderer.prototype.paragraph = function(text) {
  return '<p>' + text + '</p>\n';
};

Renderer.prototype.table = function(header, body) {
  return '<table>\n'
    + '<thead>\n'
    + header
    + '</thead>\n'
    + '<tbody>\n'
    + body
    + '</tbody>\n'
    + '</table>\n';
};

Renderer.prototype.tablerow = function(content) {
  return '<tr>\n' + content + '</tr>\n';
};

Renderer.prototype.tablecell = function(content, flags) {
  var type = flags.header ? 'th' : 'td';
  var tag = flags.align
    ? '<' + type + ' style="text-align:' + flags.align + '">'
    : '<' + type + '>';
  return tag + content + '</' + type + '>\n';
};

// span level renderer
Renderer.prototype.strong = function(text) {
  return '<strong>' + text + '</strong>';
};

Renderer.prototype.em = function(text) {
  return '<em>' + text + '</em>';
};

Renderer.prototype.codespan = function(text) {
  return '<code>' + text + '</code>';
};

Renderer.prototype.br = function() {
  return this.options.xhtml ? '<br/>' : '<br>';
};

Renderer.prototype.del = function(text) {
  return '<del>' + text + '</del>';
};

Renderer.prototype.link = function(href, title, text) {
  if (this.options.sanitize) {
    try {
      var prot = decodeURIComponent(unescape(href))
        .replace(/[^\w:]/g, '')
        .toLowerCase();
    } catch (e) {
      return text;
    }
    if (prot.indexOf('javascript:') === 0 || prot.indexOf('vbscript:') === 0 || prot.indexOf('data:') === 0) {
      return text;
    }
  }
  if (this.options.baseUrl && !originIndependentUrl.test(href)) {
    href = resolveUrl(this.options.baseUrl, href);
  }
  var out = '<a href="' + href + '"';
  if (title) {
    out += ' title="' + title + '"';
  }
  out += '>' + text + '</a>';
  return out;
};

Renderer.prototype.image = function(href, title, text) {
  if (this.options.baseUrl && !originIndependentUrl.test(href)) {
    href = resolveUrl(this.options.baseUrl, href);
  }
  var out = '<img src="' + href + '" alt="' + text + '"';
  if (title) {
    out += ' title="' + title + '"';
  }
  out += this.options.xhtml ? '/>' : '>';
  return out;
};

Renderer.prototype.text = function(text) {
  return text;
};

/**
 * TextRenderer
 * returns only the textual part of the token
 */

function TextRenderer() {}

// no need for block level renderers

TextRenderer.prototype.strong =
TextRenderer.prototype.em =
TextRenderer.prototype.codespan =
TextRenderer.prototype.del =
TextRenderer.prototype.text = function (text) {
  return text;
}

TextRenderer.prototype.link =
TextRenderer.prototype.image = function(href, title, text) {
  return '' + text;
}

TextRenderer.prototype.br = function() {
  return '';
}

/**
 * Parsing & Compiling
 */

function Parser(options) {
  this.tokens = [];
  this.token = null;
  this.options = options || marked.defaults;
  this.options.renderer = this.options.renderer || new Renderer();
  this.renderer = this.options.renderer;
  this.renderer.options = this.options;
}

/**
 * Static Parse Method
 */

Parser.parse = function(src, options) {
  var parser = new Parser(options);
  return parser.parse(src);
};

/**
 * Parse Loop
 */

Parser.prototype.parse = function(src) {
  this.inline = new InlineLexer(src.links, this.options);
  // use an InlineLexer with a TextRenderer to extract pure text
  this.inlineText = new InlineLexer(
    src.links,
    merge({}, this.options, {renderer: new TextRenderer()})
  );
  this.tokens = src.reverse();

  var out = '';
  while (this.next()) {
    out += this.tok();
  }

  return out;
};

/**
 * Next Token
 */

Parser.prototype.next = function() {
  return this.token = this.tokens.pop();
};

/**
 * Preview Next Token
 */

Parser.prototype.peek = function() {
  return this.tokens[this.tokens.length - 1] || 0;
};

/**
 * Parse Text Tokens
 */

Parser.prototype.parseText = function() {
  var body = this.token.text;

  while (this.peek().type === 'text') {
    body += '\n' + this.next().text;
  }

  return this.inline.output(body);
};

/**
 * Parse Current Token
 */

Parser.prototype.tok = function() {
  switch (this.token.type) {
    case 'space': {
      return '';
    }
    case 'hr': {
      return this.renderer.hr();
    }
    case 'heading': {
      return this.renderer.heading(
        this.inline.output(this.token.text),
        this.token.depth,
        unescape(this.inlineText.output(this.token.text)));
    }
    case 'code': {
      return this.renderer.code(this.token.text,
        this.token.lang,
        this.token.escaped);
    }
    case 'table': {
      var header = '',
          body = '',
          i,
          row,
          cell,
          j;

      // header
      cell = '';
      for (i = 0; i < this.token.header.length; i++) {
        cell += this.renderer.tablecell(
          this.inline.output(this.token.header[i]),
          { header: true, align: this.token.align[i] }
        );
      }
      header += this.renderer.tablerow(cell);

      for (i = 0; i < this.token.cells.length; i++) {
        row = this.token.cells[i];

        cell = '';
        for (j = 0; j < row.length; j++) {
          cell += this.renderer.tablecell(
            this.inline.output(row[j]),
            { header: false, align: this.token.align[j] }
          );
        }

        body += this.renderer.tablerow(cell);
      }
      return this.renderer.table(header, body);
    }
    case 'blockquote_start': {
      body = '';

      while (this.next().type !== 'blockquote_end') {
        body += this.tok();
      }

      return this.renderer.blockquote(body);
    }
    case 'list_start': {
      body = '';
      var ordered = this.token.ordered,
          start = this.token.start;

      while (this.next().type !== 'list_end') {
        body += this.tok();
      }

      return this.renderer.list(body, ordered, start);
    }
    case 'list_item_start': {
      body = '';

      while (this.next().type !== 'list_item_end') {
        body += this.token.type === 'text'
          ? this.parseText()
          : this.tok();
      }

      return this.renderer.listitem(body);
    }
    case 'loose_item_start': {
      body = '';

      while (this.next().type !== 'list_item_end') {
        body += this.tok();
      }

      return this.renderer.listitem(body);
    }
    case 'html': {
      var html = !this.token.pre && !this.options.pedantic
        ? this.inline.output(this.token.text)
        : this.token.text;
      return this.renderer.html(html);
    }
    case 'paragraph': {
      return this.renderer.paragraph(this.inline.output(this.token.text));
    }
    case 'text': {
      return this.renderer.paragraph(this.parseText());
    }
  }
};

/**
 * Helpers
 */

function escape(html, encode) {
  return html
    .replace(!encode ? /&(?!#?\w+;)/g : /&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function unescape(html) {
  // explicitly match decimal, hex, and named HTML entities
  return html.replace(/&(#(?:\d+)|(?:#x[0-9A-Fa-f]+)|(?:\w+));?/ig, function(_, n) {
    n = n.toLowerCase();
    if (n === 'colon') return ':';
    if (n.charAt(0) === '#') {
      return n.charAt(1) === 'x'
        ? String.fromCharCode(parseInt(n.substring(2), 16))
        : String.fromCharCode(+n.substring(1));
    }
    return '';
  });
}

function edit(regex, opt) {
  regex = regex.source;
  opt = opt || '';
  return {
    replace: function(name, val) {
      val = val.source || val;
      val = val.replace(/(^|[^\[])\^/g, '$1');
      regex = regex.replace(name, val);
      return this;
    },
    getRegex: function() {
      return new RegExp(regex, opt);
    }
  };
}

function resolveUrl(base, href) {
  if (!baseUrls[' ' + base]) {
    // we can ignore everything in base after the last slash of its path component,
    // but we might need to add _that_
    // https://tools.ietf.org/html/rfc3986#section-3
    if (/^[^:]+:\/*[^/]*$/.test(base)) {
      baseUrls[' ' + base] = base + '/';
    } else {
      baseUrls[' ' + base] = base.replace(/[^/]*$/, '');
    }
  }
  base = baseUrls[' ' + base];

  if (href.slice(0, 2) === '//') {
    return base.replace(/:[\s\S]*/, ':') + href;
  } else if (href.charAt(0) === '/') {
    return base.replace(/(:\/*[^/]*)[\s\S]*/, '$1') + href;
  } else {
    return base + href;
  }
}
var baseUrls = {};
var originIndependentUrl = /^$|^[a-z][a-z0-9+.-]*:|^[?#]/i;

function noop() {}
noop.exec = noop;

function merge(obj) {
  var i = 1,
      target,
      key;

  for (; i < arguments.length; i++) {
    target = arguments[i];
    for (key in target) {
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        obj[key] = target[key];
      }
    }
  }

  return obj;
}

/**
 * Marked
 */

function marked(src, opt, callback) {
  // throw error in case of non string input
  if (typeof src === 'undefined' || src === null) {
    throw new Error('marked(): input parameter is undefined or null');
  }
  if (typeof src !== 'string') {
    throw new Error('marked(): input parameter is of type '
      + Object.prototype.toString.call(src) + ', string expected');
  }

  if (callback || typeof opt === 'function') {
    if (!callback) {
      callback = opt;
      opt = null;
    }

    opt = merge({}, marked.defaults, opt || {});

    var highlight = opt.highlight,
        tokens,
        pending,
        i = 0;

    try {
      tokens = Lexer.lex(src, opt)
    } catch (e) {
      return callback(e);
    }

    pending = tokens.length;

    var done = function(err) {
      if (err) {
        opt.highlight = highlight;
        return callback(err);
      }

      var out;

      try {
        out = Parser.parse(tokens, opt);
      } catch (e) {
        err = e;
      }

      opt.highlight = highlight;

      return err
        ? callback(err)
        : callback(null, out);
    };

    if (!highlight || highlight.length < 3) {
      return done();
    }

    delete opt.highlight;

    if (!pending) return done();

    for (; i < tokens.length; i++) {
      (function(token) {
        if (token.type !== 'code') {
          return --pending || done();
        }
        return highlight(token.text, token.lang, function(err, code) {
          if (err) return done(err);
          if (code == null || code === token.text) {
            return --pending || done();
          }
          token.text = code;
          token.escaped = true;
          --pending || done();
        });
      })(tokens[i]);
    }

    return;
  }
  try {
    if (opt) opt = merge({}, marked.defaults, opt);
    return Parser.parse(Lexer.lex(src, opt), opt);
  } catch (e) {
    e.message += '\nPlease report this to https://github.com/markedjs/marked.';
    if ((opt || marked.defaults).silent) {
      return '<p>An error occurred:</p><pre>'
        + escape(e.message + '', true)
        + '</pre>';
    }
    throw e;
  }
}

/**
 * Options
 */

marked.options =
marked.setOptions = function(opt) {
  merge(marked.defaults, opt);
  return marked;
};

marked.defaults = {
  gfm: true,
  tables: true,
  breaks: false,
  pedantic: false,
  sanitize: false,
  sanitizer: null,
  mangle: true,
  smartLists: false,
  silent: false,
  highlight: null,
  langPrefix: 'lang-',
  smartypants: false,
  headerPrefix: '',
  renderer: new Renderer(),
  xhtml: false,
  baseUrl: null
};

/**
 * Expose
 */

marked.Parser = Parser;
marked.parser = Parser.parse;

marked.Renderer = Renderer;
marked.TextRenderer = TextRenderer;

marked.Lexer = Lexer;
marked.lexer = Lexer.lex;

marked.InlineLexer = InlineLexer;
marked.inlineLexer = InlineLexer.output;

marked.parse = marked;

if (typeof module !== 'undefined' && typeof exports === 'object') {
  module.exports = marked;
} else if (typeof define === 'function' && define.amd) {
  define(function() { return marked; });
} else {
  root.marked = marked;
}
})(this || (typeof window !== 'undefined' ? window : global));

(function(f){if(typeof exports==="object"&&typeof module!=="undefined"){module.exports=f()}else if(typeof define==="function"&&define.amd){define([],f)}else{var g;if(typeof window!=="undefined"){g=window}else if(typeof global!=="undefined"){g=global}else if(typeof self!=="undefined"){g=self}else{g=this}g.angularMarked = f()}})(function(){var define,module,exports;return (function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
/*
 * angular-marked
 * (c) 2014 - 2016 J. Harshbarger
 * Licensed MIT
 */

/* global angular, marked */

'use strict';

var unindent = require('./strip-indent');

  /**
   * @ngdoc overview
   * @name index
   *
   * @description
   * AngularJS Markdown using [marked](https://github.com/chjj/marked).
   *
   * ## Why?
   *
   * I wanted to use [marked](https://github.com/chjj/marked) instead of [showdown](https://github.com/coreyti/showdown) as used in [angular-markdown-directive](https://github.com/btford/angular-markdown-directive) as well as expose the option to globally set defaults.
   *
   * ## How?
   *
   * - {@link hc.marked.directive:marked As a directive}
   * - {@link hc.marked.service:marked As a service}
   * - {@link hc.marked.service:markedProvider Set default options}
   *
   * @example

      Convert markdown to html at run time.  For example:

      <example module="app">
        <file name="example.html">
          <form ng-controller="MainController">
            Markdown:<br />
            <textarea ng-model="my_markdown" cols="60" rows="5" class="span8"></textarea><br />
            Output:<br />
            <div marked="my_markdown" />
          </form>
        </file>
        <file  name="example.js">
          function MainController($scope) {
            $scope.my_markdown = "*This* **is** [markdown](https://daringfireball.net/projects/markdown/)";
          }
          angular.module('app', ['hc.marked']).controller('MainController', MainController);
        </file>
      </example>

    *
    */

    /**
     * @ngdoc overview
     * @name hc.marked
     * @description # angular-marked (core module)
       # Installation
      First include angular-marked.js in your HTML:

      ```js
        <script src="angular-marked.js">
      ```

      Then load the module in your application by adding it as a dependency:

      ```js
      angular.module('yourApp', ['hc.marked']);
      ```

      With that you're ready to get started!
     */

    /**
    * @ngdoc service
    * @name hc.marked.service:marked
    * @requires $window
    * @description
    * A reference to the [marked](https://github.com/chjj/marked) parser.
    *
    * @example
    <example module="app">
      <file name="example.html">
        <div ng-controller="MainController">
          html: {{html}}
        </div>
      </file>
      <file  name="example.js">
        function MainController($scope, marked) {
          $scope.html = marked('#TEST');
        }
        angular.module('app', ['hc.marked']).controller('MainController', MainController);
      </file>
    </example>
   **/

   /**
   * @ngdoc service
   * @name hc.marked.service:markedProvider
   * @description
   * Use `markedProvider` to change the default behavior of the {@link hc.marked.service:marked marked} service.
   *
   * @example

    ## Example using [google-code-prettify syntax highlighter](https://code.google.com/p/google-code-prettify/) (must include google-code-prettify.js script).  Also works with [highlight.js Javascript syntax highlighter](http://highlightjs.org/).

    <example module="myAppA">
      <file name="exampleA.js">
      angular.module('myAppA', ['hc.marked'])
        .config(['markedProvider', function(markedProvider) {
          markedProvider.setOptions({
            gfm: true,
            tables: true,
            highlight: function (code) {
              return prettyPrintOne(code);
            }
          });
        }]);
      </file>
      <file name="exampleA.html">
        <marked>
        ```js
        angular.module('myAppA', ['hc.marked'])
          .config(['markedProvider', function(markedProvider) {
            markedProvider.setOptions({
              gfm: true,
              tables: true,
              highlight: function (code) {
                return prettyPrintOne(code);
              }
            });
          }]);
        ```
        </marked>
      </file>
    </example>

    ## Example overriding the way custom markdown links are displayed

    <example module="myAppB">
      <file name="exampleB.js">
      angular.module('myAppB', ['hc.marked'])
        .config(['markedProvider', function(markedProvider) {
          markedProvider.setRenderer({
            link: function(href, title, text) {
              return "<a href='" + href + "'" + (title ? " title='" + title + "'" : '') + " target='_blank'>" + text + "</a>";
            }
          });
        }]);
      </file>
      <file name="exampleB.html">
        <marked>
          This is [an example](http://example.com/ "Title") inline link.
          [This link](http://example.net/) has no title attribute.
        </marked>
      </file>
    </example>
  **/

function markedProvider() {
  var self = this;

  /**
   * @ngdoc method
   * @name markedProvider#setRenderer
   * @methodOf hc.marked.service:markedProvider
   *
   * @param {object} opts Default renderer options for [marked](https://github.com/chjj/marked#overriding-renderer-methods).
   */

  self.setRenderer = function (opts) {
    this.renderer = opts;
  };

  /**
   * @ngdoc method
   * @name markedProvider#setOptions
   * @methodOf hc.marked.service:markedProvider
   *
   * @param {object} opts Default options for [marked](https://github.com/chjj/marked#options-1).
   */

  self.setOptions = function (opts) {  // Store options for later
    this.defaults = opts;
  };

  self.$get = ['$log', '$window', function ($log, $window) {
    var m;

    try {
      m = require('marked');
    } catch (err) {
      m = $window.marked || marked;
    }

    if (angular.isUndefined(m)) {
      $log.error('angular-marked Error: marked not loaded.  See installation instructions.');
      return;
    }

    var r = new m.Renderer();

    // override rendered markdown html
    // with custom definitions if defined
    if (self.renderer) {
      var o = Object.keys(self.renderer);
      var l = o.length;

      while (l--) {
        r[o[l]] = self.renderer[o[l]];
      }
    }

    // Customize code and codespan rendering to wrap default or overriden output in a ng-non-bindable span
    function wrapNonBindable(string) {
      return '<span ng-non-bindable>' + string + '</span>';
    }

    var renderCode = r.code.bind(r);
    r.code = function (code, lang, escaped) {
      return wrapNonBindable(renderCode(code, lang, escaped));
    };
    var renderCodespan = r.codespan.bind(r);
    r.codespan = function (code) {
      return wrapNonBindable(renderCodespan(code));
    };

    // add the new renderer to the options if need be
    self.defaults = self.defaults || {};
    self.defaults.renderer = r;

    m.setOptions(self.defaults);

    return m;
  }];
}

  // xTODO: filter and tests */
  // app.filter('marked', ['marked', function(marked) {
  //   return marked;
  // }]);

  /**
   * @ngdoc directive
   * @name hc.marked.directive:marked
   * @restrict AE
   * @element any
   *
   * @description
   * Compiles source test into HTML.
   *
   * @param {expression=} marked The source text to be compiled.  If blank uses content as the source.
   * @param {expression=} opts Hash of options that override defaults.
   * @param {boolean=} compile Set to true to to support AngularJS directives inside markdown.
   * @param {string=} src Expression evaluating to URL. If the source is a string constant,
   *                 make sure you wrap it in **single** quotes, e.g. `src="'myPartialTemplate.html'"`.
   *
   * @example

     ## A simple block of text

      <example module="hc.marked">
        <file name="exampleA.html">
         * <marked>
         *   ### Markdown directive
         *
         *   *It works!*
         *
         *   *This* **is** [markdown](https://daringfireball.net/projects/markdown/) in the view.
         * </marked>
        </file>
      </example>

     ## Bind to a scope variable

      <example module="app">
        <file name="exampleB.html">
          <form ng-controller="MainController">
            Markdown:<br />
            <textarea ng-model="my_markdown" class="span8" cols="60" rows="5"></textarea><br />
            Output:<br />
            <blockquote marked="my_markdown"></blockquote>
          </form>
        </file>
        <file  name="exampleB.js">
          * function MainController($scope) {
          *     $scope.my_markdown = '*This* **is** [markdown](https://daringfireball.net/projects/markdown/)';
          *     $scope.my_markdown += ' in a scope variable';
          * }
          * angular.module('app', ['hc.marked']).controller('MainController', MainController);
        </file>
      </example>

      ## Include a markdown file:

       <example module="hc.marked">
         <file name="exampleC.html">
           <div marked src="'include.html'" />
         </file>
         * <file name="include.html">
         * *This* **is** [markdown](https://daringfireball.net/projects/markdown/) in a include file.
         * </file>
       </example>
   */
markedDirective.$inject = ['marked', '$templateRequest', '$compile'];
function markedDirective(marked, $templateRequest, $compile) {
  return {
    restrict: 'AE',
    replace: true,
    scope: {
      opts: '=',
      marked: '=',
      compile: '@',
      src: '='
    },
    link: function (scope, element, attrs) {
      if (attrs.marked) {
        set(scope.marked);
        scope.$watch('marked', set);
      } else if (attrs.src) {
        scope.$watch('src', function (src) {
          $templateRequest(src, true).then(function (response) {
            set(response);
          }, function () {
            set('');
            scope.$emit('$markedIncludeError', attrs.src);
          });
        });
      } else {
        set(element.text());
      }

      function set(text) {
        text = unindent(String(text || ''));
        element.html(marked(text, scope.opts || null));
        if (scope.$eval(attrs.compile)) {
          $compile(element.contents())(scope.$parent);
        }
      }
    }
  };
}

module.exports =
  angular.module('hc.marked', [])
    .directive('marked', markedDirective)
    .provider('marked', markedProvider)
    .name;

},{"./strip-indent":2,"marked":"marked"}],2:[function(require,module,exports){
module.exports = function unindent(text) {
  if (!text) {
    return text;
  }

  var lines = text
    .replace(/\t/g, '  ')
    .split(/\r?\n/);

  var min = null;
  var len = lines.length;
  var i;

  for (i = 0; i < len; i++) {
    var line = lines[i];
    var l = line.match(/^(\s*)/)[0].length;
    if (l === line.length) {
      continue;
    }
    min = (l < min || min === null) ? l : min;
  }

  if (min !== null && min > 0) {
    for (i = 0; i < len; i++) {
      lines[i] = lines[i].substr(min);
    }
  }
  return lines.join('\n');
};

},{}]},{},[1])(1)
});
/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

var forEach;
var isArray;
var isString;
var jqLite;

/**
 * @ngdoc module
 * @name ngMessages
 * @description
 *
 * The `ngMessages` module provides enhanced support for displaying messages within templates
 * (typically within forms or when rendering message objects that return key/value data).
 * Instead of relying on JavaScript code and/or complex ng-if statements within your form template to
 * show and hide error messages specific to the state of an input field, the `ngMessages` and
 * `ngMessage` directives are designed to handle the complexity, inheritance and priority
 * sequencing based on the order of how the messages are defined in the template.
 *
 * Currently, the ngMessages module only contains the code for the `ngMessages`, `ngMessagesInclude`
 * `ngMessage`, `ngMessageExp` and `ngMessageDefault` directives.
 *
 * ## Usage
 * The `ngMessages` directive allows keys in a key/value collection to be associated with a child element
 * (or 'message') that will show or hide based on the truthiness of that key's value in the collection. A common use
 * case for `ngMessages` is to display error messages for inputs using the `$error` object exposed by the
 * {@link ngModel ngModel} directive.
 *
 * The child elements of the `ngMessages` directive are matched to the collection keys by a `ngMessage` or
 * `ngMessageExp` directive. The value of these attributes must match a key in the collection that is provided by
 * the `ngMessages` directive.
 *
 * Consider the following example, which illustrates a typical use case of `ngMessages`. Within the form `myForm` we
 * have a text input named `myField` which is bound to the scope variable `field` using the {@link ngModel ngModel}
 * directive.
 *
 * The `myField` field is a required input of type `email` with a maximum length of 15 characters.
 *
 * ```html
 * <form name="myForm">
 *   <label>
 *     Enter text:
 *     <input type="email" ng-model="field" name="myField" required maxlength="15" />
 *   </label>
 *   <div ng-messages="myForm.myField.$error" role="alert">
 *     <div ng-message="required">Please enter a value for this field.</div>
 *     <div ng-message="email">This field must be a valid email address.</div>
 *     <div ng-message="maxlength">This field can be at most 15 characters long.</div>
 *   </div>
 * </form>
 * ```
 *
 * In order to show error messages corresponding to `myField` we first create an element with an `ngMessages` attribute
 * set to the `$error` object owned by the `myField` input in our `myForm` form.
 *
 * Within this element we then create separate elements for each of the possible errors that `myField` could have.
 * The `ngMessage` attribute is used to declare which element(s) will appear for which error - for example,
 * setting `ng-message="required"` specifies that this particular element should be displayed when there
 * is no value present for the required field `myField` (because the key `required` will be `true` in the object
 * `myForm.myField.$error`).
 *
 * ### Message order
 *
 * By default, `ngMessages` will only display one message for a particular key/value collection at any time. If more
 * than one message (or error) key is currently true, then which message is shown is determined by the order of messages
 * in the HTML template code (messages declared first are prioritised). This mechanism means the developer does not have
 * to prioritize messages using custom JavaScript code.
 *
 * Given the following error object for our example (which informs us that the field `myField` currently has both the
 * `required` and `email` errors):
 *
 * ```javascript
 * <!-- keep in mind that ngModel automatically sets these error flags -->
 * myField.$error = { required : true, email: true, maxlength: false };
 * ```
 * The `required` message will be displayed to the user since it appears before the `email` message in the DOM.
 * Once the user types a single character, the `required` message will disappear (since the field now has a value)
 * but the `email` message will be visible because it is still applicable.
 *
 * ### Displaying multiple messages at the same time
 *
 * While `ngMessages` will by default only display one error element at a time, the `ng-messages-multiple` attribute can
 * be applied to the `ngMessages` container element to cause it to display all applicable error messages at once:
 *
 * ```html
 * <!-- attribute-style usage -->
 * <div ng-messages="myForm.myField.$error" ng-messages-multiple>...</div>
 *
 * <!-- element-style usage -->
 * <ng-messages for="myForm.myField.$error" multiple>...</ng-messages>
 * ```
 *
 * ## Reusing and Overriding Messages
 * In addition to prioritization, ngMessages also allows for including messages from a remote or an inline
 * template. This allows for generic collection of messages to be reused across multiple parts of an
 * application.
 *
 * ```html
 * <script type="text/ng-template" id="error-messages">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 * </script>
 *
 * <div ng-messages="myForm.myField.$error" role="alert">
 *   <div ng-messages-include="error-messages"></div>
 * </div>
 * ```
 *
 * However, including generic messages may not be useful enough to match all input fields, therefore,
 * `ngMessages` provides the ability to override messages defined in the remote template by redefining
 * them within the directive container.
 *
 * ```html
 * <!-- a generic template of error messages known as "my-custom-messages" -->
 * <script type="text/ng-template" id="my-custom-messages">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 * </script>
 *
 * <form name="myForm">
 *   <label>
 *     Email address
 *     <input type="email"
 *            id="email"
 *            name="myEmail"
 *            ng-model="email"
 *            minlength="5"
 *            required />
 *   </label>
 *   <!-- any ng-message elements that appear BEFORE the ng-messages-include will
 *        override the messages present in the ng-messages-include template -->
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <!-- this required message has overridden the template message -->
 *     <div ng-message="required">You did not enter your email address</div>
 *
 *     <!-- this is a brand new message and will appear last in the prioritization -->
 *     <div ng-message="email">Your email address is invalid</div>
 *
 *     <!-- and here are the generic error messages -->
 *     <div ng-messages-include="my-custom-messages"></div>
 *   </div>
 * </form>
 * ```
 *
 * In the example HTML code above the message that is set on required will override the corresponding
 * required message defined within the remote template. Therefore, with particular input fields (such
 * email addresses, date fields, autocomplete inputs, etc...), specialized error messages can be applied
 * while more generic messages can be used to handle other, more general input errors.
 *
 * ## Dynamic Messaging
 * ngMessages also supports using expressions to dynamically change key values. Using arrays and
 * repeaters to list messages is also supported. This means that the code below will be able to
 * fully adapt itself and display the appropriate message when any of the expression data changes:
 *
 * ```html
 * <form name="myForm">
 *   <label>
 *     Email address
 *     <input type="email"
 *            name="myEmail"
 *            ng-model="email"
 *            minlength="5"
 *            required />
 *   </label>
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <div ng-message="required">You did not enter your email address</div>
 *     <div ng-repeat="errorMessage in errorMessages">
 *       <!-- use ng-message-exp for a message whose key is given by an expression -->
 *       <div ng-message-exp="errorMessage.type">{{ errorMessage.text }}</div>
 *     </div>
 *   </div>
 * </form>
 * ```
 *
 * The `errorMessage.type` expression can be a string value or it can be an array so
 * that multiple errors can be associated with a single error message:
 *
 * ```html
 *   <label>
 *     Email address
 *     <input type="email"
 *            ng-model="data.email"
 *            name="myEmail"
 *            ng-minlength="5"
 *            ng-maxlength="100"
 *            required />
 *   </label>
 *   <div ng-messages="myForm.myEmail.$error" role="alert">
 *     <div ng-message-exp="'required'">You did not enter your email address</div>
 *     <div ng-message-exp="['minlength', 'maxlength']">
 *       Your email must be between 5 and 100 characters long
 *     </div>
 *   </div>
 * ```
 *
 * Feel free to use other structural directives such as ng-if and ng-switch to further control
 * what messages are active and when. Be careful, if you place ng-message on the same element
 * as these structural directives, AngularJS may not be able to determine if a message is active
 * or not. Therefore it is best to place the ng-message on a child element of the structural
 * directive.
 *
 * ```html
 * <div ng-messages="myForm.myEmail.$error" role="alert">
 *   <div ng-if="showRequiredError">
 *     <div ng-message="required">Please enter something</div>
 *   </div>
 * </div>
 * ```
 *
 * ## Animations
 * If the `ngAnimate` module is active within the application then the `ngMessages`, `ngMessage` and
 * `ngMessageExp` directives will trigger animations whenever any messages are added and removed from
 * the DOM by the `ngMessages` directive.
 *
 * Whenever the `ngMessages` directive contains one or more visible messages then the `.ng-active` CSS
 * class will be added to the element. The `.ng-inactive` CSS class will be applied when there are no
 * messages present. Therefore, CSS transitions and keyframes as well as JavaScript animations can
 * hook into the animations whenever these classes are added/removed.
 *
 * Let's say that our HTML code for our messages container looks like so:
 *
 * ```html
 * <div ng-messages="myMessages" class="my-messages" role="alert">
 *   <div ng-message="alert" class="some-message">...</div>
 *   <div ng-message="fail" class="some-message">...</div>
 * </div>
 * ```
 *
 * Then the CSS animation code for the message container looks like so:
 *
 * ```css
 * .my-messages {
 *   transition:1s linear all;
 * }
 * .my-messages.ng-active {
 *   // messages are visible
 * }
 * .my-messages.ng-inactive {
 *   // messages are hidden
 * }
 * ```
 *
 * Whenever an inner message is attached (becomes visible) or removed (becomes hidden) then the enter
 * and leave animation is triggered for each particular element bound to the `ngMessage` directive.
 *
 * Therefore, the CSS code for the inner messages looks like so:
 *
 * ```css
 * .some-message {
 *   transition:1s linear all;
 * }
 *
 * .some-message.ng-enter {}
 * .some-message.ng-enter.ng-enter-active {}
 *
 * .some-message.ng-leave {}
 * .some-message.ng-leave.ng-leave-active {}
 * ```
 *
 * {@link ngAnimate See the ngAnimate docs} to learn how to use JavaScript animations or to learn
 * more about ngAnimate.
 *
 * ## Displaying a default message
 * If the ngMessages renders no inner ngMessage directive (i.e. when none of the truthy
 * keys are matched by a defined message), then it will render a default message
 * using the {@link ngMessageDefault} directive.
 * Note that matched messages will always take precedence over unmatched messages. That means
 * the default message will not be displayed when another message is matched. This is also
 * true for `ng-messages-multiple`.
 *
 * ```html
 * <div ng-messages="myForm.myField.$error" role="alert">
 *   <div ng-message="required">This field is required</div>
 *   <div ng-message="minlength">This field is too short</div>
 *   <div ng-message-default>This field has an input error</div>
 * </div>
 * ```
 *

 */
angular.module('ngMessages', [], function initAngularHelpers() {
  // Access helpers from AngularJS core.
  // Do it inside a `config` block to ensure `window.angular` is available.
  forEach = angular.forEach;
  isArray = angular.isArray;
  isString = angular.isString;
  jqLite = angular.element;
})
  .info({ angularVersion: '1.8.3' })

  /**
   * @ngdoc directive
   * @module ngMessages
   * @name ngMessages
   * @restrict AE
   *
   * @description
   * `ngMessages` is a directive that is designed to show and hide messages based on the state
   * of a key/value object that it listens on. The directive itself complements error message
   * reporting with the `ngModel` $error object (which stores a key/value state of validation errors).
   *
   * `ngMessages` manages the state of internal messages within its container element. The internal
   * messages use the `ngMessage` directive and will be inserted/removed from the page depending
   * on if they're present within the key/value object. By default, only one message will be displayed
   * at a time and this depends on the prioritization of the messages within the template. (This can
   * be changed by using the `ng-messages-multiple` or `multiple` attribute on the directive container.)
   *
   * A remote template can also be used (With {@link ngMessagesInclude}) to promote message
   * reusability and messages can also be overridden.
   *
   * A default message can also be displayed when no `ngMessage` directive is inserted, using the
   * {@link ngMessageDefault} directive.
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   *   <ANY ng-message-exp="expressionValue">...</ANY>
   *   <ANY ng-message-default>...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   *   <ng-message when-exp="expressionValue">...</ng-message>
   *   <ng-message-default>...</ng-message-default>
   * </ng-messages>
   * ```
   *
   * @param {string} ngMessages an AngularJS expression evaluating to a key/value object
   *                 (this is typically the $error object on an ngModel instance).
   * @param {string=} ngMessagesMultiple|multiple when set, all messages will be displayed with true
   *
   * @example
   * <example name="ngMessages-directive" module="ngMessagesExample"
   *          deps="angular-messages.js"
   *          animations="true" fixBase="true">
   *   <file name="index.html">
   *     <form name="myForm">
   *       <label>
   *         Enter your name:
   *         <input type="text"
   *                name="myName"
   *                ng-model="name"
   *                ng-minlength="5"
   *                ng-maxlength="20"
   *                required />
   *       </label>
   *       <pre>myForm.myName.$error = {{ myForm.myName.$error | json }}</pre>
   *
   *       <div ng-messages="myForm.myName.$error" style="color:maroon" role="alert">
   *         <div ng-message="required">You did not enter a field</div>
   *         <div ng-message="minlength">Your field is too short</div>
   *         <div ng-message="maxlength">Your field is too long</div>
   *         <div ng-message-default>This field has an input error</div>
   *       </div>
   *     </form>
   *   </file>
   *   <file name="script.js">
   *     angular.module('ngMessagesExample', ['ngMessages']);
   *   </file>
   * </example>
   */
  .directive('ngMessages', ['$animate', function($animate) {
    var ACTIVE_CLASS = 'ng-active';
    var INACTIVE_CLASS = 'ng-inactive';

    return {
      require: 'ngMessages',
      restrict: 'AE',
      controller: ['$element', '$scope', '$attrs', function NgMessagesCtrl($element, $scope, $attrs) {
        var ctrl = this;
        var latestKey = 0;
        var nextAttachId = 0;

        this.getAttachId = function getAttachId() { return nextAttachId++; };

        var messages = this.messages = {};
        var renderLater, cachedCollection;

        this.render = function(collection) {
          collection = collection || {};

          renderLater = false;
          cachedCollection = collection;

          // this is true if the attribute is empty or if the attribute value is truthy
          var multiple = isAttrTruthy($scope, $attrs.ngMessagesMultiple) ||
                         isAttrTruthy($scope, $attrs.multiple);

          var unmatchedMessages = [];
          var matchedKeys = {};
          var truthyKeys = 0;
          var messageItem = ctrl.head;
          var messageFound = false;
          var totalMessages = 0;

          // we use != instead of !== to allow for both undefined and null values
          while (messageItem != null) {
            totalMessages++;
            var messageCtrl = messageItem.message;

            var messageUsed = false;
            if (!messageFound) {
              forEach(collection, function(value, key) {
                if (truthy(value) && !messageUsed) {
                  truthyKeys++;

                  if (messageCtrl.test(key)) {
                    // this is to prevent the same error name from showing up twice
                    if (matchedKeys[key]) return;
                    matchedKeys[key] = true;

                    messageUsed = true;
                    messageCtrl.attach();
                  }
                }
              });
            }

            if (messageUsed) {
              // unless we want to display multiple messages then we should
              // set a flag here to avoid displaying the next message in the list
              messageFound = !multiple;
            } else {
              unmatchedMessages.push(messageCtrl);
            }

            messageItem = messageItem.next;
          }

          forEach(unmatchedMessages, function(messageCtrl) {
            messageCtrl.detach();
          });

          var messageMatched = unmatchedMessages.length !== totalMessages;
          var attachDefault = ctrl.default && !messageMatched && truthyKeys > 0;

          if (attachDefault) {
            ctrl.default.attach();
          } else if (ctrl.default) {
            ctrl.default.detach();
          }

          if (messageMatched || attachDefault) {
            $animate.setClass($element, ACTIVE_CLASS, INACTIVE_CLASS);
          } else {
            $animate.setClass($element, INACTIVE_CLASS, ACTIVE_CLASS);
          }
        };

        $scope.$watchCollection($attrs.ngMessages || $attrs['for'], ctrl.render);

        this.reRender = function() {
          if (!renderLater) {
            renderLater = true;
            $scope.$evalAsync(function() {
              if (renderLater && cachedCollection) {
                ctrl.render(cachedCollection);
              }
            });
          }
        };

        this.register = function(comment, messageCtrl, isDefault) {
          if (isDefault) {
            ctrl.default = messageCtrl;
          } else {
            var nextKey = latestKey.toString();
            messages[nextKey] = {
              message: messageCtrl
            };
            insertMessageNode($element[0], comment, nextKey);
            comment.$$ngMessageNode = nextKey;
            latestKey++;
          }

          ctrl.reRender();
        };

        this.deregister = function(comment, isDefault) {
          if (isDefault) {
            delete ctrl.default;
          } else {
            var key = comment.$$ngMessageNode;
            delete comment.$$ngMessageNode;
            removeMessageNode($element[0], comment, key);
            delete messages[key];
          }
          ctrl.reRender();
        };

        function findPreviousMessage(parent, comment) {
          var prevNode = comment;
          var parentLookup = [];

          while (prevNode && prevNode !== parent) {
            var prevKey = prevNode.$$ngMessageNode;
            if (prevKey && prevKey.length) {
              return messages[prevKey];
            }

            // dive deeper into the DOM and examine its children for any ngMessage
            // comments that may be in an element that appears deeper in the list
            if (prevNode.childNodes.length && parentLookup.indexOf(prevNode) === -1) {
              parentLookup.push(prevNode);
              prevNode = prevNode.childNodes[prevNode.childNodes.length - 1];
            } else if (prevNode.previousSibling) {
              prevNode = prevNode.previousSibling;
            } else {
              prevNode = prevNode.parentNode;
              parentLookup.push(prevNode);
            }
          }
        }

        function insertMessageNode(parent, comment, key) {
          var messageNode = messages[key];
          if (!ctrl.head) {
            ctrl.head = messageNode;
          } else {
            var match = findPreviousMessage(parent, comment);
            if (match) {
              messageNode.next = match.next;
              match.next = messageNode;
            } else {
              messageNode.next = ctrl.head;
              ctrl.head = messageNode;
            }
          }
        }

        function removeMessageNode(parent, comment, key) {
          var messageNode = messages[key];

          // This message node may have already been removed by a call to deregister()
          if (!messageNode) return;

          var match = findPreviousMessage(parent, comment);
          if (match) {
            match.next = messageNode.next;
          } else {
            ctrl.head = messageNode.next;
          }
        }
      }]
    };

    function isAttrTruthy(scope, attr) {
     return (isString(attr) && attr.length === 0) || //empty attribute
            truthy(scope.$eval(attr));
    }

    function truthy(val) {
      return isString(val) ? val.length : !!val;
    }
  }])

  /**
   * @ngdoc directive
   * @name ngMessagesInclude
   * @restrict AE
   * @scope
   *
   * @description
   * `ngMessagesInclude` is a directive with the purpose to import existing ngMessage template
   * code from a remote template and place the downloaded template code into the exact spot
   * that the ngMessagesInclude directive is placed within the ngMessages container. This allows
   * for a series of pre-defined messages to be reused and also allows for the developer to
   * determine what messages are overridden due to the placement of the ngMessagesInclude directive.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-messages-include="remoteTplString">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-messages-include src="expressionValue1">...</ng-messages-include>
   * </ng-messages>
   * ```
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @param {string} ngMessagesInclude|src a string value corresponding to the remote template.
   */
  .directive('ngMessagesInclude',
    ['$templateRequest', '$document', '$compile', function($templateRequest, $document, $compile) {

    return {
      restrict: 'AE',
      require: '^^ngMessages', // we only require this for validation sake
      link: function($scope, element, attrs) {
        var src = attrs.ngMessagesInclude || attrs.src;
        $templateRequest(src).then(function(html) {
          if ($scope.$$destroyed) return;

          if (isString(html) && !html.trim()) {
            // Empty template - nothing to compile
            replaceElementWithMarker(element, src);
          } else {
            // Non-empty template - compile and link
            $compile(html)($scope, function(contents) {
              element.after(contents);
              replaceElementWithMarker(element, src);
            });
          }
        });
      }
    };

    // Helpers
    function replaceElementWithMarker(element, src) {
      // A comment marker is placed for debugging purposes
      var comment = $compile.$$createComment ?
          $compile.$$createComment('ngMessagesInclude', src) :
          $document[0].createComment(' ngMessagesInclude: ' + src + ' ');
      var marker = jqLite(comment);
      element.after(marker);

      // Don't pollute the DOM anymore by keeping an empty directive element
      element.remove();
    }
  }])

  /**
   * @ngdoc directive
   * @name ngMessage
   * @restrict AE
   * @scope
   * @priority 1
   *
   * @description
   * `ngMessage` is a directive with the purpose to show and hide a particular message.
   * For `ngMessage` to operate, a parent `ngMessages` directive on a parent DOM element
   * must be situated since it determines which messages are visible based on the state
   * of the provided key/value map that `ngMessages` listens on.
   *
   * More information about using `ngMessage` can be found in the
   * {@link module:ngMessages `ngMessages` module documentation}.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   * </ng-messages>
   * ```
   *
   * @param {expression} ngMessage|when a string value corresponding to the message key.
   */
  .directive('ngMessage', ngMessageDirectiveFactory())


  /**
   * @ngdoc directive
   * @name ngMessageExp
   * @restrict AE
   * @priority 1
   * @scope
   *
   * @description
   * `ngMessageExp` is the same as {@link directive:ngMessage `ngMessage`}, but instead of a static
   * value, it accepts an expression to be evaluated for the message key.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression">
   *   <ANY ng-message-exp="expressionValue">...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression">
   *   <ng-message when-exp="expressionValue">...</ng-message>
   * </ng-messages>
   * ```
   *
   * {@link module:ngMessages Click here} to learn more about `ngMessages` and `ngMessage`.
   *
   * @param {expression} ngMessageExp|whenExp an expression value corresponding to the message key.
   */
  .directive('ngMessageExp', ngMessageDirectiveFactory())

  /**
   * @ngdoc directive
   * @name ngMessageDefault
   * @restrict AE
   * @scope
   *
   * @description
   * `ngMessageDefault` is a directive with the purpose to show and hide a default message for
   * {@link directive:ngMessages}, when none of provided messages matches.
   *
   * More information about using `ngMessageDefault` can be found in the
   * {@link module:ngMessages `ngMessages` module documentation}.
   *
   * @usage
   * ```html
   * <!-- using attribute directives -->
   * <ANY ng-messages="expression" role="alert">
   *   <ANY ng-message="stringValue">...</ANY>
   *   <ANY ng-message="stringValue1, stringValue2, ...">...</ANY>
   *   <ANY ng-message-default>...</ANY>
   * </ANY>
   *
   * <!-- or by using element directives -->
   * <ng-messages for="expression" role="alert">
   *   <ng-message when="stringValue">...</ng-message>
   *   <ng-message when="stringValue1, stringValue2, ...">...</ng-message>
   *   <ng-message-default>...</ng-message-default>
   * </ng-messages>
   *
  */
  .directive('ngMessageDefault', ngMessageDirectiveFactory(true));

function ngMessageDirectiveFactory(isDefault) {
  return ['$animate', function($animate) {
    return {
      restrict: 'AE',
      transclude: 'element',
      priority: 1, // must run before ngBind, otherwise the text is set on the comment
      terminal: true,
      require: '^^ngMessages',
      link: function(scope, element, attrs, ngMessagesCtrl, $transclude) {
        var commentNode, records, staticExp, dynamicExp;

        if (!isDefault) {
          commentNode = element[0];
          staticExp = attrs.ngMessage || attrs.when;
          dynamicExp = attrs.ngMessageExp || attrs.whenExp;

          var assignRecords = function(items) {
            records = items
                ? (isArray(items)
                      ? items
                      : items.split(/[\s,]+/))
                : null;
            ngMessagesCtrl.reRender();
          };

          if (dynamicExp) {
            assignRecords(scope.$eval(dynamicExp));
            scope.$watchCollection(dynamicExp, assignRecords);
          } else {
            assignRecords(staticExp);
          }
        }

        var currentElement, messageCtrl;
        ngMessagesCtrl.register(commentNode, messageCtrl = {
          test: function(name) {
            return contains(records, name);
          },
          attach: function() {
            if (!currentElement) {
              $transclude(function(elm, newScope) {
                $animate.enter(elm, null, element);
                currentElement = elm;

                // Each time we attach this node to a message we get a new id that we can match
                // when we are destroying the node later.
                var $$attachId = currentElement.$$attachId = ngMessagesCtrl.getAttachId();

                // in the event that the element or a parent element is destroyed
                // by another structural directive then it's time
                // to deregister the message from the controller
                currentElement.on('$destroy', function() {
                  // If the message element was removed via a call to `detach` then `currentElement` will be null
                  // So this handler only handles cases where something else removed the message element.
                  if (currentElement && currentElement.$$attachId === $$attachId) {
                    ngMessagesCtrl.deregister(commentNode, isDefault);
                    messageCtrl.detach();
                  }
                  newScope.$destroy();
                });
              });
            }
          },
          detach: function() {
            if (currentElement) {
              var elm = currentElement;
              currentElement = null;
              $animate.leave(elm);
            }
          }
        }, isDefault);

        // We need to ensure that this directive deregisters itself when it no longer exists
        // Normally this is done when the attached element is destroyed; but if this directive
        // gets removed before we attach the message to the DOM there is nothing to watch
        // in which case we must deregister when the containing scope is destroyed.
        scope.$on('$destroy', function() {
          ngMessagesCtrl.deregister(commentNode, isDefault);
        });
      }
    };
  }];

  function contains(collection, key) {
    if (collection) {
      return isArray(collection)
          ? collection.indexOf(key) >= 0
          : collection.hasOwnProperty(key);
    }
  }
}


})(window, window.angular);

/**
 * @license AngularJS v1.8.3
 * (c) 2010-2020 Google LLC. http://angularjs.org
 * License: MIT
 */
(function(window, angular) {'use strict';

/**
 * @ngdoc module
 * @name ngAria
 * @description
 *
 * The `ngAria` module provides support for common
 * [<abbr title="Accessible Rich Internet Applications">ARIA</abbr>](http://www.w3.org/TR/wai-aria/)
 * attributes that convey state or semantic information about the application for users
 * of assistive technologies, such as screen readers.
 *
 * ## Usage
 *
 * For ngAria to do its magic, simply include the module `ngAria` as a dependency. The following
 * directives are supported:
 * `ngModel`, `ngChecked`, `ngReadonly`, `ngRequired`, `ngValue`, `ngDisabled`, `ngShow`, `ngHide`,
 * `ngClick`, `ngDblClick`, and `ngMessages`.
 *
 * Below is a more detailed breakdown of the attributes handled by ngAria:
 *
 * | Directive                                   | Supported Attributes                                                                                |
 * |---------------------------------------------|-----------------------------------------------------------------------------------------------------|
 * | {@link ng.directive:ngModel ngModel}        | aria-checked, aria-valuemin, aria-valuemax, aria-valuenow, aria-invalid, aria-required, input roles |
 * | {@link ng.directive:ngDisabled ngDisabled}  | aria-disabled                                                                                       |
 * | {@link ng.directive:ngRequired ngRequired}  | aria-required                                                                                       |
 * | {@link ng.directive:ngChecked ngChecked}    | aria-checked                                                                                        |
 * | {@link ng.directive:ngReadonly ngReadonly}  | aria-readonly                                                                                       |
 * | {@link ng.directive:ngValue ngValue}        | aria-checked                                                                                        |
 * | {@link ng.directive:ngShow ngShow}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngHide ngHide}          | aria-hidden                                                                                         |
 * | {@link ng.directive:ngDblclick ngDblclick}  | tabindex                                                                                            |
 * | {@link module:ngMessages ngMessages}        | aria-live                                                                                           |
 * | {@link ng.directive:ngClick ngClick}        | tabindex, keydown event, button role                                                                |
 *
 * Find out more information about each directive by reading the
 * {@link guide/accessibility ngAria Developer Guide}.
 *
 * ## Example
 * Using ngDisabled with ngAria:
 * ```html
 * <md-checkbox ng-disabled="disabled">
 * ```
 * Becomes:
 * ```html
 * <md-checkbox ng-disabled="disabled" aria-disabled="true">
 * ```
 *
 * ## Disabling Specific Attributes
 * It is possible to disable individual attributes added by ngAria with the
 * {@link ngAria.$ariaProvider#config config} method. For more details, see the
 * {@link guide/accessibility Developer Guide}.
 *
 * ## Disabling `ngAria` on Specific Elements
 * It is possible to make `ngAria` ignore a specific element, by adding the `ng-aria-disable`
 * attribute on it. Note that only the element itself (and not its child elements) will be ignored.
 */
var ARIA_DISABLE_ATTR = 'ngAriaDisable';

var ngAriaModule = angular.module('ngAria', ['ng']).
                        info({ angularVersion: '1.8.3' }).
                        provider('$aria', $AriaProvider);

/**
* Internal Utilities
*/
var nativeAriaNodeNames = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'DETAILS', 'SUMMARY'];

var isNodeOneOf = function(elem, nodeTypeArray) {
  if (nodeTypeArray.indexOf(elem[0].nodeName) !== -1) {
    return true;
  }
};
/**
 * @ngdoc provider
 * @name $ariaProvider
 * @this
 *
 * @description
 *
 * Used for configuring the ARIA attributes injected and managed by ngAria.
 *
 * ```js
 * angular.module('myApp', ['ngAria'], function config($ariaProvider) {
 *   $ariaProvider.config({
 *     ariaValue: true,
 *     tabindex: false
 *   });
 * });
 *```
 *
 * ## Dependencies
 * Requires the {@link ngAria} module to be installed.
 *
 */
function $AriaProvider() {
  var config = {
    ariaHidden: true,
    ariaChecked: true,
    ariaReadonly: true,
    ariaDisabled: true,
    ariaRequired: true,
    ariaInvalid: true,
    ariaValue: true,
    tabindex: true,
    bindKeydown: true,
    bindRoleForClick: true
  };

  /**
   * @ngdoc method
   * @name $ariaProvider#config
   *
   * @param {object} config object to enable/disable specific ARIA attributes
   *
   *  - **ariaHidden** – `{boolean}` – Enables/disables aria-hidden tags
   *  - **ariaChecked** – `{boolean}` – Enables/disables aria-checked tags
   *  - **ariaReadonly** – `{boolean}` – Enables/disables aria-readonly tags
   *  - **ariaDisabled** – `{boolean}` – Enables/disables aria-disabled tags
   *  - **ariaRequired** – `{boolean}` – Enables/disables aria-required tags
   *  - **ariaInvalid** – `{boolean}` – Enables/disables aria-invalid tags
   *  - **ariaValue** – `{boolean}` – Enables/disables aria-valuemin, aria-valuemax and
   *    aria-valuenow tags
   *  - **tabindex** – `{boolean}` – Enables/disables tabindex tags
   *  - **bindKeydown** – `{boolean}` – Enables/disables keyboard event binding on non-interactive
   *    elements (such as `div` or `li`) using ng-click, making them more accessible to users of
   *    assistive technologies
   *  - **bindRoleForClick** – `{boolean}` – Adds role=button to non-interactive elements (such as
   *    `div` or `li`) using ng-click, making them more accessible to users of assistive
   *    technologies
   *
   * @description
   * Enables/disables various ARIA attributes
   */
  this.config = function(newConfig) {
    config = angular.extend(config, newConfig);
  };

  function watchExpr(attrName, ariaAttr, nativeAriaNodeNames, negate) {
    return function(scope, elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var ariaCamelName = attr.$normalize(ariaAttr);
      if (config[ariaCamelName] && !isNodeOneOf(elem, nativeAriaNodeNames) && !attr[ariaCamelName]) {
        scope.$watch(attr[attrName], function(boolVal) {
          // ensure boolean value
          boolVal = negate ? !boolVal : !!boolVal;
          elem.attr(ariaAttr, boolVal);
        });
      }
    };
  }
  /**
   * @ngdoc service
   * @name $aria
   *
   * @description
   *
   * The $aria service contains helper methods for applying common
   * [ARIA](http://www.w3.org/TR/wai-aria/) attributes to HTML directives.
   *
   * ngAria injects common accessibility attributes that tell assistive technologies when HTML
   * elements are enabled, selected, hidden, and more. To see how this is performed with ngAria,
   * let's review a code snippet from ngAria itself:
   *
   *```js
   * ngAriaModule.directive('ngDisabled', ['$aria', function($aria) {
   *   return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nativeAriaNodeNames, false);
   * }])
   *```
   * Shown above, the ngAria module creates a directive with the same signature as the
   * traditional `ng-disabled` directive. But this ngAria version is dedicated to
   * solely managing accessibility attributes on custom elements. The internal `$aria` service is
   * used to watch the boolean attribute `ngDisabled`. If it has not been explicitly set by the
   * developer, `aria-disabled` is injected as an attribute with its value synchronized to the
   * value in `ngDisabled`.
   *
   * Because ngAria hooks into the `ng-disabled` directive, developers do not have to do
   * anything to enable this feature. The `aria-disabled` attribute is automatically managed
   * simply as a silent side-effect of using `ng-disabled` with the ngAria module.
   *
   * The full list of directives that interface with ngAria:
   * * **ngModel**
   * * **ngChecked**
   * * **ngReadonly**
   * * **ngRequired**
   * * **ngDisabled**
   * * **ngValue**
   * * **ngShow**
   * * **ngHide**
   * * **ngClick**
   * * **ngDblclick**
   * * **ngMessages**
   *
   * Read the {@link guide/accessibility ngAria Developer Guide} for a thorough explanation of each
   * directive.
   *
   *
   * ## Dependencies
   * Requires the {@link ngAria} module to be installed.
   */
  this.$get = function() {
    return {
      config: function(key) {
        return config[key];
      },
      $$watchExpr: watchExpr
    };
  };
}


ngAriaModule.directive('ngShow', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngShow', 'aria-hidden', [], true);
}])
.directive('ngHide', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngHide', 'aria-hidden', [], false);
}])
.directive('ngValue', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngValue', 'aria-checked', nativeAriaNodeNames, false);
}])
.directive('ngChecked', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngChecked', 'aria-checked', nativeAriaNodeNames, false);
}])
.directive('ngReadonly', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngReadonly', 'aria-readonly', nativeAriaNodeNames, false);
}])
.directive('ngRequired', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngRequired', 'aria-required', nativeAriaNodeNames, false);
}])
.directive('ngModel', ['$aria', function($aria) {

  function shouldAttachAttr(attr, normalizedAttr, elem, allowNonAriaNodes) {
    return $aria.config(normalizedAttr) &&
      !elem.attr(attr) &&
      (allowNonAriaNodes || !isNodeOneOf(elem, nativeAriaNodeNames)) &&
      (elem.attr('type') !== 'hidden' || elem[0].nodeName !== 'INPUT');
  }

  function shouldAttachRole(role, elem) {
    // if element does not have role attribute
    // AND element type is equal to role (if custom element has a type equaling shape) <-- remove?
    // AND element is not in nativeAriaNodeNames
    return !elem.attr('role') && (elem.attr('type') === role) && !isNodeOneOf(elem, nativeAriaNodeNames);
  }

  function getShape(attr, elem) {
    var type = attr.type,
        role = attr.role;

    return ((type || role) === 'checkbox' || role === 'menuitemcheckbox') ? 'checkbox' :
           ((type || role) === 'radio'    || role === 'menuitemradio') ? 'radio' :
           (type === 'range'              || role === 'progressbar' || role === 'slider') ? 'range' : '';
  }

  return {
    restrict: 'A',
    require: 'ngModel',
    priority: 200, //Make sure watches are fired after any other directives that affect the ngModel value
    compile: function(elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var shape = getShape(attr, elem);

      return {
        post: function(scope, elem, attr, ngModel) {
          var needsTabIndex = shouldAttachAttr('tabindex', 'tabindex', elem, false);

          function ngAriaWatchModelValue() {
            return ngModel.$modelValue;
          }

          function getRadioReaction(newVal) {
            // Strict comparison would cause a BC
            // eslint-disable-next-line eqeqeq
            var boolVal = (attr.value == ngModel.$viewValue);
            elem.attr('aria-checked', boolVal);
          }

          function getCheckboxReaction() {
            elem.attr('aria-checked', !ngModel.$isEmpty(ngModel.$viewValue));
          }

          switch (shape) {
            case 'radio':
            case 'checkbox':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', shape);
              }
              if (shouldAttachAttr('aria-checked', 'ariaChecked', elem, false)) {
                scope.$watch(ngAriaWatchModelValue, shape === 'radio' ?
                    getRadioReaction : getCheckboxReaction);
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
            case 'range':
              if (shouldAttachRole(shape, elem)) {
                elem.attr('role', 'slider');
              }
              if ($aria.config('ariaValue')) {
                var needsAriaValuemin = !elem.attr('aria-valuemin') &&
                    (attr.hasOwnProperty('min') || attr.hasOwnProperty('ngMin'));
                var needsAriaValuemax = !elem.attr('aria-valuemax') &&
                    (attr.hasOwnProperty('max') || attr.hasOwnProperty('ngMax'));
                var needsAriaValuenow = !elem.attr('aria-valuenow');

                if (needsAriaValuemin) {
                  attr.$observe('min', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemin', newVal);
                  });
                }
                if (needsAriaValuemax) {
                  attr.$observe('max', function ngAriaValueMinReaction(newVal) {
                    elem.attr('aria-valuemax', newVal);
                  });
                }
                if (needsAriaValuenow) {
                  scope.$watch(ngAriaWatchModelValue, function ngAriaValueNowReaction(newVal) {
                    elem.attr('aria-valuenow', newVal);
                  });
                }
              }
              if (needsTabIndex) {
                elem.attr('tabindex', 0);
              }
              break;
          }

          if (!attr.hasOwnProperty('ngRequired') && ngModel.$validators.required
            && shouldAttachAttr('aria-required', 'ariaRequired', elem, false)) {
            // ngModel.$error.required is undefined on custom controls
            attr.$observe('required', function() {
              elem.attr('aria-required', !!attr['required']);
            });
          }

          if (shouldAttachAttr('aria-invalid', 'ariaInvalid', elem, true)) {
            scope.$watch(function ngAriaInvalidWatch() {
              return ngModel.$invalid;
            }, function ngAriaInvalidReaction(newVal) {
              elem.attr('aria-invalid', !!newVal);
            });
          }
        }
      };
    }
  };
}])
.directive('ngDisabled', ['$aria', function($aria) {
  return $aria.$$watchExpr('ngDisabled', 'aria-disabled', nativeAriaNodeNames, false);
}])
.directive('ngMessages', function() {
  return {
    restrict: 'A',
    require: '?ngMessages',
    link: function(scope, elem, attr, ngMessages) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      if (!elem.attr('aria-live')) {
        elem.attr('aria-live', 'assertive');
      }
    }
  };
})
.directive('ngClick',['$aria', '$parse', function($aria, $parse) {
  return {
    restrict: 'A',
    compile: function(elem, attr) {
      if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

      var fn = $parse(attr.ngClick);
      return function(scope, elem, attr) {

        if (!isNodeOneOf(elem, nativeAriaNodeNames)) {

          if ($aria.config('bindRoleForClick') && !elem.attr('role')) {
            elem.attr('role', 'button');
          }

          if ($aria.config('tabindex') && !elem.attr('tabindex')) {
            elem.attr('tabindex', 0);
          }

          if ($aria.config('bindKeydown') && !attr.ngKeydown && !attr.ngKeypress && !attr.ngKeyup) {
            elem.on('keydown', function(event) {
              var keyCode = event.which || event.keyCode;

              if (keyCode === 13 || keyCode === 32) {
                // If the event is triggered on a non-interactive element ...
                if (nativeAriaNodeNames.indexOf(event.target.nodeName) === -1 && !event.target.isContentEditable) {
                  // ... prevent the default browser behavior (e.g. scrolling when pressing spacebar)
                  // See https://github.com/angular/angular.js/issues/16664
                  event.preventDefault();
                }
                scope.$apply(callback);
              }

              function callback() {
                fn(scope, { $event: event });
              }
            });
          }
        }
      };
    }
  };
}])
.directive('ngDblclick', ['$aria', function($aria) {
  return function(scope, elem, attr) {
    if (attr.hasOwnProperty(ARIA_DISABLE_ATTR)) return;

    if ($aria.config('tabindex') && !elem.attr('tabindex') && !isNodeOneOf(elem, nativeAriaNodeNames)) {
      elem.attr('tabindex', 0);
    }
  };
}]);


})(window, window.angular);

/*!
 * jQuery JavaScript Library v3.5.1
 * https://jquery.com/
 *
 * Includes Sizzle.js
 * https://sizzlejs.com/
 *
 * Copyright JS Foundation and other contributors
 * Released under the MIT license
 * https://jquery.org/license
 *
 * Date: 2020-05-04T22:49Z
 */
( function( global, factory ) {

	"use strict";

	if ( typeof module === "object" && typeof module.exports === "object" ) {

		// For CommonJS and CommonJS-like environments where a proper `window`
		// is present, execute the factory and get jQuery.
		// For environments that do not have a `window` with a `document`
		// (such as Node.js), expose a factory as module.exports.
		// This accentuates the need for the creation of a real `window`.
		// e.g. var jQuery = require("jquery")(window);
		// See ticket #14549 for more info.
		module.exports = global.document ?
			factory( global, true ) :
			function( w ) {
				if ( !w.document ) {
					throw new Error( "jQuery requires a window with a document" );
				}
				return factory( w );
			};
	} else {
		factory( global );
	}

// Pass this if window is not defined yet
} )( typeof window !== "undefined" ? window : this, function( window, noGlobal ) {

// Edge <= 12 - 13+, Firefox <=18 - 45+, IE 10 - 11, Safari 5.1 - 9+, iOS 6 - 9.1
// throw exceptions when non-strict code (e.g., ASP.NET 4.5) accesses strict mode
// arguments.callee.caller (trac-13335). But as of jQuery 3.0 (2016), strict mode should be common
// enough that all such attempts are guarded in a try block.
"use strict";

var arr = [];

var getProto = Object.getPrototypeOf;

var slice = arr.slice;

var flat = arr.flat ? function( array ) {
	return arr.flat.call( array );
} : function( array ) {
	return arr.concat.apply( [], array );
};


var push = arr.push;

var indexOf = arr.indexOf;

var class2type = {};

var toString = class2type.toString;

var hasOwn = class2type.hasOwnProperty;

var fnToString = hasOwn.toString;

var ObjectFunctionString = fnToString.call( Object );

var support = {};

var isFunction = function isFunction( obj ) {

      // Support: Chrome <=57, Firefox <=52
      // In some browsers, typeof returns "function" for HTML <object> elements
      // (i.e., `typeof document.createElement( "object" ) === "function"`).
      // We don't want to classify *any* DOM node as a function.
      return typeof obj === "function" && typeof obj.nodeType !== "number";
  };


var isWindow = function isWindow( obj ) {
		return obj != null && obj === obj.window;
	};


var document = window.document;



	var preservedScriptAttributes = {
		type: true,
		src: true,
		nonce: true,
		noModule: true
	};

	function DOMEval( code, node, doc ) {
		doc = doc || document;

		var i, val,
			script = doc.createElement( "script" );

		script.text = code;
		if ( node ) {
			for ( i in preservedScriptAttributes ) {

				// Support: Firefox 64+, Edge 18+
				// Some browsers don't support the "nonce" property on scripts.
				// On the other hand, just using `getAttribute` is not enough as
				// the `nonce` attribute is reset to an empty string whenever it
				// becomes browsing-context connected.
				// See https://github.com/whatwg/html/issues/2369
				// See https://html.spec.whatwg.org/#nonce-attributes
				// The `node.getAttribute` check was added for the sake of
				// `jQuery.globalEval` so that it can fake a nonce-containing node
				// via an object.
				val = node[ i ] || node.getAttribute && node.getAttribute( i );
				if ( val ) {
					script.setAttribute( i, val );
				}
			}
		}
		doc.head.appendChild( script ).parentNode.removeChild( script );
	}


function toType( obj ) {
	if ( obj == null ) {
		return obj + "";
	}

	// Support: Android <=2.3 only (functionish RegExp)
	return typeof obj === "object" || typeof obj === "function" ?
		class2type[ toString.call( obj ) ] || "object" :
		typeof obj;
}
/* global Symbol */
// Defining this global in .eslintrc.json would create a danger of using the global
// unguarded in another place, it seems safer to define global only for this module



var
	version = "3.5.1",

	// Define a local copy of jQuery
	jQuery = function( selector, context ) {

		// The jQuery object is actually just the init constructor 'enhanced'
		// Need init if jQuery is called (just allow error to be thrown if not included)
		return new jQuery.fn.init( selector, context );
	};

jQuery.fn = jQuery.prototype = {

	// The current version of jQuery being used
	jquery: version,

	constructor: jQuery,

	// The default length of a jQuery object is 0
	length: 0,

	toArray: function() {
		return slice.call( this );
	},

	// Get the Nth element in the matched element set OR
	// Get the whole matched element set as a clean array
	get: function( num ) {

		// Return all the elements in a clean array
		if ( num == null ) {
			return slice.call( this );
		}

		// Return just the one element from the set
		return num < 0 ? this[ num + this.length ] : this[ num ];
	},

	// Take an array of elements and push it onto the stack
	// (returning the new matched element set)
	pushStack: function( elems ) {

		// Build a new jQuery matched element set
		var ret = jQuery.merge( this.constructor(), elems );

		// Add the old object onto the stack (as a reference)
		ret.prevObject = this;

		// Return the newly-formed element set
		return ret;
	},

	// Execute a callback for every element in the matched set.
	each: function( callback ) {
		return jQuery.each( this, callback );
	},

	map: function( callback ) {
		return this.pushStack( jQuery.map( this, function( elem, i ) {
			return callback.call( elem, i, elem );
		} ) );
	},

	slice: function() {
		return this.pushStack( slice.apply( this, arguments ) );
	},

	first: function() {
		return this.eq( 0 );
	},

	last: function() {
		return this.eq( -1 );
	},

	even: function() {
		return this.pushStack( jQuery.grep( this, function( _elem, i ) {
			return ( i + 1 ) % 2;
		} ) );
	},

	odd: function() {
		return this.pushStack( jQuery.grep( this, function( _elem, i ) {
			return i % 2;
		} ) );
	},

	eq: function( i ) {
		var len = this.length,
			j = +i + ( i < 0 ? len : 0 );
		return this.pushStack( j >= 0 && j < len ? [ this[ j ] ] : [] );
	},

	end: function() {
		return this.prevObject || this.constructor();
	},

	// For internal use only.
	// Behaves like an Array's method, not like a jQuery method.
	push: push,
	sort: arr.sort,
	splice: arr.splice
};

jQuery.extend = jQuery.fn.extend = function() {
	var options, name, src, copy, copyIsArray, clone,
		target = arguments[ 0 ] || {},
		i = 1,
		length = arguments.length,
		deep = false;

	// Handle a deep copy situation
	if ( typeof target === "boolean" ) {
		deep = target;

		// Skip the boolean and the target
		target = arguments[ i ] || {};
		i++;
	}

	// Handle case when target is a string or something (possible in deep copy)
	if ( typeof target !== "object" && !isFunction( target ) ) {
		target = {};
	}

	// Extend jQuery itself if only one argument is passed
	if ( i === length ) {
		target = this;
		i--;
	}

	for ( ; i < length; i++ ) {

		// Only deal with non-null/undefined values
		if ( ( options = arguments[ i ] ) != null ) {

			// Extend the base object
			for ( name in options ) {
				copy = options[ name ];

				// Prevent Object.prototype pollution
				// Prevent never-ending loop
				if ( name === "__proto__" || target === copy ) {
					continue;
				}

				// Recurse if we're merging plain objects or arrays
				if ( deep && copy && ( jQuery.isPlainObject( copy ) ||
					( copyIsArray = Array.isArray( copy ) ) ) ) {
					src = target[ name ];

					// Ensure proper type for the source value
					if ( copyIsArray && !Array.isArray( src ) ) {
						clone = [];
					} else if ( !copyIsArray && !jQuery.isPlainObject( src ) ) {
						clone = {};
					} else {
						clone = src;
					}
					copyIsArray = false;

					// Never move original objects, clone them
					target[ name ] = jQuery.extend( deep, clone, copy );

				// Don't bring in undefined values
				} else if ( copy !== undefined ) {
					target[ name ] = copy;
				}
			}
		}
	}

	// Return the modified object
	return target;
};

jQuery.extend( {

	// Unique for each copy of jQuery on the page
	expando: "jQuery" + ( version + Math.random() ).replace( /\D/g, "" ),

	// Assume jQuery is ready without the ready module
	isReady: true,

	error: function( msg ) {
		throw new Error( msg );
	},

	noop: function() {},

	isPlainObject: function( obj ) {
		var proto, Ctor;

		// Detect obvious negatives
		// Use toString instead of jQuery.type to catch host objects
		if ( !obj || toString.call( obj ) !== "[object Object]" ) {
			return false;
		}

		proto = getProto( obj );

		// Objects with no prototype (e.g., `Object.create( null )`) are plain
		if ( !proto ) {
			return true;
		}

		// Objects with prototype are plain iff they were constructed by a global Object function
		Ctor = hasOwn.call( proto, "constructor" ) && proto.constructor;
		return typeof Ctor === "function" && fnToString.call( Ctor ) === ObjectFunctionString;
	},

	isEmptyObject: function( obj ) {
		var name;

		for ( name in obj ) {
			return false;
		}
		return true;
	},

	// Evaluates a script in a provided context; falls back to the global one
	// if not specified.
	globalEval: function( code, options, doc ) {
		DOMEval( code, { nonce: options && options.nonce }, doc );
	},

	each: function( obj, callback ) {
		var length, i = 0;

		if ( isArrayLike( obj ) ) {
			length = obj.length;
			for ( ; i < length; i++ ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		} else {
			for ( i in obj ) {
				if ( callback.call( obj[ i ], i, obj[ i ] ) === false ) {
					break;
				}
			}
		}

		return obj;
	},

	// results is for internal usage only
	makeArray: function( arr, results ) {
		var ret = results || [];

		if ( arr != null ) {
			if ( isArrayLike( Object( arr ) ) ) {
				jQuery.merge( ret,
					typeof arr === "string" ?
					[ arr ] : arr
				);
			} else {
				push.call( ret, arr );
			}
		}

		return ret;
	},

	inArray: function( elem, arr, i ) {
		return arr == null ? -1 : indexOf.call( arr, elem, i );
	},

	// Support: Android <=4.0 only, PhantomJS 1 only
	// push.apply(_, arraylike) throws on ancient WebKit
	merge: function( first, second ) {
		var len = +second.length,
			j = 0,
			i = first.length;

		for ( ; j < len; j++ ) {
			first[ i++ ] = second[ j ];
		}

		first.length = i;

		return first;
	},

	grep: function( elems, callback, invert ) {
		var callbackInverse,
			matches = [],
			i = 0,
			length = elems.length,
			callbackExpect = !invert;

		// Go through the array, only saving the items
		// that pass the validator function
		for ( ; i < length; i++ ) {
			callbackInverse = !callback( elems[ i ], i );
			if ( callbackInverse !== callbackExpect ) {
				matches.push( elems[ i ] );
			}
		}

		return matches;
	},

	// arg is for internal usage only
	map: function( elems, callback, arg ) {
		var length, value,
			i = 0,
			ret = [];

		// Go through the array, translating each of the items to their new values
		if ( isArrayLike( elems ) ) {
			length = elems.length;
			for ( ; i < length; i++ ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}

		// Go through every key on the object,
		} else {
			for ( i in elems ) {
				value = callback( elems[ i ], i, arg );

				if ( value != null ) {
					ret.push( value );
				}
			}
		}

		// Flatten any nested arrays
		return flat( ret );
	},

	// A global GUID counter for objects
	guid: 1,

	// jQuery.support is not used in Core but other projects attach their
	// properties to it so it needs to exist.
	support: support
} );

if ( typeof Symbol === "function" ) {
	jQuery.fn[ Symbol.iterator ] = arr[ Symbol.iterator ];
}

// Populate the class2type map
jQuery.each( "Boolean Number String Function Array Date RegExp Object Error Symbol".split( " " ),
function( _i, name ) {
	class2type[ "[object " + name + "]" ] = name.toLowerCase();
} );

function isArrayLike( obj ) {

	// Support: real iOS 8.2 only (not reproducible in simulator)
	// `in` check used to prevent JIT error (gh-2145)
	// hasOwn isn't used here due to false negatives
	// regarding Nodelist length in IE
	var length = !!obj && "length" in obj && obj.length,
		type = toType( obj );

	if ( isFunction( obj ) || isWindow( obj ) ) {
		return false;
	}

	return type === "array" || length === 0 ||
		typeof length === "number" && length > 0 && ( length - 1 ) in obj;
}
var Sizzle =
/*!
 * Sizzle CSS Selector Engine v2.3.5
 * https://sizzlejs.com/
 *
 * Copyright JS Foundation and other contributors
 * Released under the MIT license
 * https://js.foundation/
 *
 * Date: 2020-03-14
 */
( function( window ) {
var i,
	support,
	Expr,
	getText,
	isXML,
	tokenize,
	compile,
	select,
	outermostContext,
	sortInput,
	hasDuplicate,

	// Local document vars
	setDocument,
	document,
	docElem,
	documentIsHTML,
	rbuggyQSA,
	rbuggyMatches,
	matches,
	contains,

	// Instance-specific data
	expando = "sizzle" + 1 * new Date(),
	preferredDoc = window.document,
	dirruns = 0,
	done = 0,
	classCache = createCache(),
	tokenCache = createCache(),
	compilerCache = createCache(),
	nonnativeSelectorCache = createCache(),
	sortOrder = function( a, b ) {
		if ( a === b ) {
			hasDuplicate = true;
		}
		return 0;
	},

	// Instance methods
	hasOwn = ( {} ).hasOwnProperty,
	arr = [],
	pop = arr.pop,
	pushNative = arr.push,
	push = arr.push,
	slice = arr.slice,

	// Use a stripped-down indexOf as it's faster than native
	// https://jsperf.com/thor-indexof-vs-for/5
	indexOf = function( list, elem ) {
		var i = 0,
			len = list.length;
		for ( ; i < len; i++ ) {
			if ( list[ i ] === elem ) {
				return i;
			}
		}
		return -1;
	},

	booleans = "checked|selected|async|autofocus|autoplay|controls|defer|disabled|hidden|" +
		"ismap|loop|multiple|open|readonly|required|scoped",

	// Regular expressions

	// http://www.w3.org/TR/css3-selectors/#whitespace
	whitespace = "[\\x20\\t\\r\\n\\f]",

	// https://www.w3.org/TR/css-syntax-3/#ident-token-diagram
	identifier = "(?:\\\\[\\da-fA-F]{1,6}" + whitespace +
		"?|\\\\[^\\r\\n\\f]|[\\w-]|[^\0-\\x7f])+",

	// Attribute selectors: http://www.w3.org/TR/selectors/#attribute-selectors
	attributes = "\\[" + whitespace + "*(" + identifier + ")(?:" + whitespace +

		// Operator (capture 2)
		"*([*^$|!~]?=)" + whitespace +

		// "Attribute values must be CSS identifiers [capture 5]
		// or strings [capture 3 or capture 4]"
		"*(?:'((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\"|(" + identifier + "))|)" +
		whitespace + "*\\]",

	pseudos = ":(" + identifier + ")(?:\\((" +

		// To reduce the number of selectors needing tokenize in the preFilter, prefer arguments:
		// 1. quoted (capture 3; capture 4 or capture 5)
		"('((?:\\\\.|[^\\\\'])*)'|\"((?:\\\\.|[^\\\\\"])*)\")|" +

		// 2. simple (capture 6)
		"((?:\\\\.|[^\\\\()[\\]]|" + attributes + ")*)|" +

		// 3. anything else (capture 2)
		".*" +
		")\\)|)",

	// Leading and non-escaped trailing whitespace, capturing some non-whitespace characters preceding the latter
	rwhitespace = new RegExp( whitespace + "+", "g" ),
	rtrim = new RegExp( "^" + whitespace + "+|((?:^|[^\\\\])(?:\\\\.)*)" +
		whitespace + "+$", "g" ),

	rcomma = new RegExp( "^" + whitespace + "*," + whitespace + "*" ),
	rcombinators = new RegExp( "^" + whitespace + "*([>+~]|" + whitespace + ")" + whitespace +
		"*" ),
	rdescend = new RegExp( whitespace + "|>" ),

	rpseudo = new RegExp( pseudos ),
	ridentifier = new RegExp( "^" + identifier + "$" ),

	matchExpr = {
		"ID": new RegExp( "^#(" + identifier + ")" ),
		"CLASS": new RegExp( "^\\.(" + identifier + ")" ),
		"TAG": new RegExp( "^(" + identifier + "|[*])" ),
		"ATTR": new RegExp( "^" + attributes ),
		"PSEUDO": new RegExp( "^" + pseudos ),
		"CHILD": new RegExp( "^:(only|first|last|nth|nth-last)-(child|of-type)(?:\\(" +
			whitespace + "*(even|odd|(([+-]|)(\\d*)n|)" + whitespace + "*(?:([+-]|)" +
			whitespace + "*(\\d+)|))" + whitespace + "*\\)|)", "i" ),
		"bool": new RegExp( "^(?:" + booleans + ")$", "i" ),

		// For use in libraries implementing .is()
		// We use this for POS matching in `select`
		"needsContext": new RegExp( "^" + whitespace +
			"*[>+~]|:(even|odd|eq|gt|lt|nth|first|last)(?:\\(" + whitespace +
			"*((?:-\\d)?\\d*)" + whitespace + "*\\)|)(?=[^-]|$)", "i" )
	},

	rhtml = /HTML$/i,
	rinputs = /^(?:input|select|textarea|button)$/i,
	rheader = /^h\d$/i,

	rnative = /^[^{]+\{\s*\[native \w/,

	// Easily-parseable/retrievable ID or TAG or CLASS selectors
	rquickExpr = /^(?:#([\w-]+)|(\w+)|\.([\w-]+))$/,

	rsibling = /[+~]/,

	// CSS escapes
	// http://www.w3.org/TR/CSS21/syndata.html#escaped-characters
	runescape = new RegExp( "\\\\[\\da-fA-F]{1,6}" + whitespace + "?|\\\\([^\\r\\n\\f])", "g" ),
	funescape = function( escape, nonHex ) {
		var high = "0x" + escape.slice( 1 ) - 0x10000;

		return nonHex ?

			// Strip the backslash prefix from a non-hex escape sequence
			nonHex :

			// Replace a hexadecimal escape sequence with the encoded Unicode code point
			// Support: IE <=11+
			// For values outside the Basic Multilingual Plane (BMP), manually construct a
			// surrogate pair
			high < 0 ?
				String.fromCharCode( high + 0x10000 ) :
				String.fromCharCode( high >> 10 | 0xD800, high & 0x3FF | 0xDC00 );
	},

	// CSS string/identifier serialization
	// https://drafts.csswg.org/cssom/#common-serializing-idioms
	rcssescape = /([\0-\x1f\x7f]|^-?\d)|^-$|[^\0-\x1f\x7f-\uFFFF\w-]/g,
	fcssescape = function( ch, asCodePoint ) {
		if ( asCodePoint ) {

			// U+0000 NULL becomes U+FFFD REPLACEMENT CHARACTER
			if ( ch === "\0" ) {
				return "\uFFFD";
			}

			// Control characters and (dependent upon position) numbers get escaped as code points
			return ch.slice( 0, -1 ) + "\\" +
				ch.charCodeAt( ch.length - 1 ).toString( 16 ) + " ";
		}

		// Other potentially-special ASCII characters get backslash-escaped
		return "\\" + ch;
	},

	// Used for iframes
	// See setDocument()
	// Removing the function wrapper causes a "Permission Denied"
	// error in IE
	unloadHandler = function() {
		setDocument();
	},

	inDisabledFieldset = addCombinator(
		function( elem ) {
			return elem.disabled === true && elem.nodeName.toLowerCase() === "fieldset";
		},
		{ dir: "parentNode", next: "legend" }
	);

// Optimize for push.apply( _, NodeList )
try {
	push.apply(
		( arr = slice.call( preferredDoc.childNodes ) ),
		preferredDoc.childNodes
	);

	// Support: Android<4.0
	// Detect silently failing push.apply
	// eslint-disable-next-line no-unused-expressions
	arr[ preferredDoc.childNodes.length ].nodeType;
} catch ( e ) {
	push = { apply: arr.length ?

		// Leverage slice if possible
		function( target, els ) {
			pushNative.apply( target, slice.call( els ) );
		} :

		// Support: IE<9
		// Otherwise append directly
		function( target, els ) {
			var j = target.length,
				i = 0;

			// Can't trust NodeList.length
			while ( ( target[ j++ ] = els[ i++ ] ) ) {}
			target.length = j - 1;
		}
	};
}

function Sizzle( selector, context, results, seed ) {
	var m, i, elem, nid, match, groups, newSelector,
		newContext = context && context.ownerDocument,

		// nodeType defaults to 9, since context defaults to document
		nodeType = context ? context.nodeType : 9;

	results = results || [];

	// Return early from calls with invalid selector or context
	if ( typeof selector !== "string" || !selector ||
		nodeType !== 1 && nodeType !== 9 && nodeType !== 11 ) {

		return results;
	}

	// Try to shortcut find operations (as opposed to filters) in HTML documents
	if ( !seed ) {
		setDocument( context );
		context = context || document;

		if ( documentIsHTML ) {

			// If the selector is sufficiently simple, try using a "get*By*" DOM method
			// (excepting DocumentFragment context, where the methods don't exist)
			if ( nodeType !== 11 && ( match = rquickExpr.exec( selector ) ) ) {

				// ID selector
				if ( ( m = match[ 1 ] ) ) {

					// Document context
					if ( nodeType === 9 ) {
						if ( ( elem = context.getElementById( m ) ) ) {

							// Support: IE, Opera, Webkit
							// TODO: identify versions
							// getElementById can match elements by name instead of ID
							if ( elem.id === m ) {
								results.push( elem );
								return results;
							}
						} else {
							return results;
						}

					// Element context
					} else {

						// Support: IE, Opera, Webkit
						// TODO: identify versions
						// getElementById can match elements by name instead of ID
						if ( newContext && ( elem = newContext.getElementById( m ) ) &&
							contains( context, elem ) &&
							elem.id === m ) {

							results.push( elem );
							return results;
						}
					}

				// Type selector
				} else if ( match[ 2 ] ) {
					push.apply( results, context.getElementsByTagName( selector ) );
					return results;

				// Class selector
				} else if ( ( m = match[ 3 ] ) && support.getElementsByClassName &&
					context.getElementsByClassName ) {

					push.apply( results, context.getElementsByClassName( m ) );
					return results;
				}
			}

			// Take advantage of querySelectorAll
			if ( support.qsa &&
				!nonnativeSelectorCache[ selector + " " ] &&
				( !rbuggyQSA || !rbuggyQSA.test( selector ) ) &&

				// Support: IE 8 only
				// Exclude object elements
				( nodeType !== 1 || context.nodeName.toLowerCase() !== "object" ) ) {

				newSelector = selector;
				newContext = context;

				// qSA considers elements outside a scoping root when evaluating child or
				// descendant combinators, which is not what we want.
				// In such cases, we work around the behavior by prefixing every selector in the
				// list with an ID selector referencing the scope context.
				// The technique has to be used as well when a leading combinator is used
				// as such selectors are not recognized by querySelectorAll.
				// Thanks to Andrew Dupont for this technique.
				if ( nodeType === 1 &&
					( rdescend.test( selector ) || rcombinators.test( selector ) ) ) {

					// Expand context for sibling selectors
					newContext = rsibling.test( selector ) && testContext( context.parentNode ) ||
						context;

					// We can use :scope instead of the ID hack if the browser
					// supports it & if we're not changing the context.
					if ( newContext !== context || !support.scope ) {

						// Capture the context ID, setting it first if necessary
						if ( ( nid = context.getAttribute( "id" ) ) ) {
							nid = nid.replace( rcssescape, fcssescape );
						} else {
							context.setAttribute( "id", ( nid = expando ) );
						}
					}

					// Prefix every selector in the list
					groups = tokenize( selector );
					i = groups.length;
					while ( i-- ) {
						groups[ i ] = ( nid ? "#" + nid : ":scope" ) + " " +
							toSelector( groups[ i ] );
					}
					newSelector = groups.join( "," );
				}

				try {
					push.apply( results,
						newContext.querySelectorAll( newSelector )
					);
					return results;
				} catch ( qsaError ) {
					nonnativeSelectorCache( selector, true );
				} finally {
					if ( nid === expando ) {
						context.removeAttribute( "id" );
					}
				}
			}
		}
	}

	// All others
	return select( selector.replace( rtrim, "$1" ), context, results, seed );
}

/**
 * Create key-value caches of limited size
 * @returns {function(string, object)} Returns the Object data after storing it on itself with
 *	property name the (space-suffixed) string and (if the cache is larger than Expr.cacheLength)
 *	deleting the oldest entry
 */
function createCache() {
	var keys = [];

	function cache( key, value ) {

		// Use (key + " ") to avoid collision with native prototype properties (see Issue #157)
		if ( keys.push( key + " " ) > Expr.cacheLength ) {

			// Only keep the most recent entries
			delete cache[ keys.shift() ];
		}
		return ( cache[ key + " " ] = value );
	}
	return cache;
}

/**
 * Mark a function for special use by Sizzle
 * @param {Function} fn The function to mark
 */
function markFunction( fn ) {
	fn[ expando ] = true;
	return fn;
}

/**
 * Support testing using an element
 * @param {Function} fn Passed the created element and returns a boolean result
 */
function assert( fn ) {
	var el = document.createElement( "fieldset" );

	try {
		return !!fn( el );
	} catch ( e ) {
		return false;
	} finally {

		// Remove from its parent by default
		if ( el.parentNode ) {
			el.parentNode.removeChild( el );
		}

		// release memory in IE
		el = null;
	}
}

/**
 * Adds the same handler for all of the specified attrs
 * @param {String} attrs Pipe-separated list of attributes
 * @param {Function} handler The method that will be applied
 */
function addHandle( attrs, handler ) {
	var arr = attrs.split( "|" ),
		i = arr.length;

	while ( i-- ) {
		Expr.attrHandle[ arr[ i ] ] = handler;
	}
}

/**
 * Checks document order of two siblings
 * @param {Element} a
 * @param {Element} b
 * @returns {Number} Returns less than 0 if a precedes b, greater than 0 if a follows b
 */
function siblingCheck( a, b ) {
	var cur = b && a,
		diff = cur && a.nodeType === 1 && b.nodeType === 1 &&
			a.sourceIndex - b.sourceIndex;

	// Use IE sourceIndex if available on both nodes
	if ( diff ) {
		return diff;
	}

	// Check if b follows a
	if ( cur ) {
		while ( ( cur = cur.nextSibling ) ) {
			if ( cur === b ) {
				return -1;
			}
		}
	}

	return a ? 1 : -1;
}

/**
 * Returns a function to use in pseudos for input types
 * @param {String} type
 */
function createInputPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return name === "input" && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for buttons
 * @param {String} type
 */
function createButtonPseudo( type ) {
	return function( elem ) {
		var name = elem.nodeName.toLowerCase();
		return ( name === "input" || name === "button" ) && elem.type === type;
	};
}

/**
 * Returns a function to use in pseudos for :enabled/:disabled
 * @param {Boolean} disabled true for :disabled; false for :enabled
 */
function createDisabledPseudo( disabled ) {

	// Known :disabled false positives: fieldset[disabled] > legend:nth-of-type(n+2) :can-disable
	return function( elem ) {

		// Only certain elements can match :enabled or :disabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-enabled
		// https://html.spec.whatwg.org/multipage/scripting.html#selector-disabled
		if ( "form" in elem ) {

			// Check for inherited disabledness on relevant non-disabled elements:
			// * listed form-associated elements in a disabled fieldset
			//   https://html.spec.whatwg.org/multipage/forms.html#category-listed
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-fe-disabled
			// * option elements in a disabled optgroup
			//   https://html.spec.whatwg.org/multipage/forms.html#concept-option-disabled
			// All such elements have a "form" property.
			if ( elem.parentNode && elem.disabled === false ) {

				// Option elements defer to a parent optgroup if present
				if ( "label" in elem ) {
					if ( "label" in elem.parentNode ) {
						return elem.parentNode.disabled === disabled;
					} else {
						return elem.disabled === disabled;
					}
				}

				// Support: IE 6 - 11
				// Use the isDisabled shortcut property to check for disabled fieldset ancestors
				return elem.isDisabled === disabled ||

					// Where there is no isDisabled, check manually
					/* jshint -W018 */
					elem.isDisabled !== !disabled &&
					inDisabledFieldset( elem ) === disabled;
			}

			return elem.disabled === disabled;

		// Try to winnow out elements that can't be disabled before trusting the disabled property.
		// Some victims get caught in our net (label, legend, menu, track), but it shouldn't
		// even exist on them, let alone have a boolean value.
		} else if ( "label" in elem ) {
			return elem.disabled === disabled;
		}

		// Remaining elements are neither :enabled nor :disabled
		return false;
	};
}

/**
 * Returns a function to use in pseudos for positionals
 * @param {Function} fn
 */
function createPositionalPseudo( fn ) {
	return markFunction( function( argument ) {
		argument = +argument;
		return markFunction( function( seed, matches ) {
			var j,
				matchIndexes = fn( [], seed.length, argument ),
				i = matchIndexes.length;

			// Match elements found at the specified indexes
			while ( i-- ) {
				if ( seed[ ( j = matchIndexes[ i ] ) ] ) {
					seed[ j ] = !( matches[ j ] = seed[ j ] );
				}
			}
		} );
	} );
}

/**
 * Checks a node for validity as a Sizzle context
 * @param {Element|Object=} context
 * @returns {Element|Object|Boolean} The input node if acceptable, otherwise a falsy value
 */
function testContext( context ) {
	return context && typeof context.getElementsByTagName !== "undefined" && context;
}

// Expose support vars for convenience
support = Sizzle.support = {};

/**
 * Detects XML nodes
 * @param {Element|Object} elem An element or a document
 * @returns {Boolean} True iff elem is a non-HTML XML node
 */
isXML = Sizzle.isXML = function( elem ) {
	var namespace = elem.namespaceURI,
		docElem = ( elem.ownerDocument || elem ).documentElement;

	// Support: IE <=8
	// Assume HTML when documentElement doesn't yet exist, such as inside loading iframes
	// https://bugs.jquery.com/ticket/4833
	return !rhtml.test( namespace || docElem && docElem.nodeName || "HTML" );
};

/**
 * Sets document-related variables once based on the current document
 * @param {Element|Object} [doc] An element or document object to use to set the document
 * @returns {Object} Returns the current document
 */
setDocument = Sizzle.setDocument = function( node ) {
	var hasCompare, subWindow,
		doc = node ? node.ownerDocument || node : preferredDoc;

	// Return early if doc is invalid or already selected
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( doc == document || doc.nodeType !== 9 || !doc.documentElement ) {
		return document;
	}

	// Update global variables
	document = doc;
	docElem = document.documentElement;
	documentIsHTML = !isXML( document );

	// Support: IE 9 - 11+, Edge 12 - 18+
	// Accessing iframe documents after unload throws "permission denied" errors (jQuery #13936)
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( preferredDoc != document &&
		( subWindow = document.defaultView ) && subWindow.top !== subWindow ) {

		// Support: IE 11, Edge
		if ( subWindow.addEventListener ) {
			subWindow.addEventListener( "unload", unloadHandler, false );

		// Support: IE 9 - 10 only
		} else if ( subWindow.attachEvent ) {
			subWindow.attachEvent( "onunload", unloadHandler );
		}
	}

	// Support: IE 8 - 11+, Edge 12 - 18+, Chrome <=16 - 25 only, Firefox <=3.6 - 31 only,
	// Safari 4 - 5 only, Opera <=11.6 - 12.x only
	// IE/Edge & older browsers don't support the :scope pseudo-class.
	// Support: Safari 6.0 only
	// Safari 6.0 supports :scope but it's an alias of :root there.
	support.scope = assert( function( el ) {
		docElem.appendChild( el ).appendChild( document.createElement( "div" ) );
		return typeof el.querySelectorAll !== "undefined" &&
			!el.querySelectorAll( ":scope fieldset div" ).length;
	} );

	/* Attributes
	---------------------------------------------------------------------- */

	// Support: IE<8
	// Verify that getAttribute really returns attributes and not properties
	// (excepting IE8 booleans)
	support.attributes = assert( function( el ) {
		el.className = "i";
		return !el.getAttribute( "className" );
	} );

	/* getElement(s)By*
	---------------------------------------------------------------------- */

	// Check if getElementsByTagName("*") returns only elements
	support.getElementsByTagName = assert( function( el ) {
		el.appendChild( document.createComment( "" ) );
		return !el.getElementsByTagName( "*" ).length;
	} );

	// Support: IE<9
	support.getElementsByClassName = rnative.test( document.getElementsByClassName );

	// Support: IE<10
	// Check if getElementById returns elements by name
	// The broken getElementById methods don't pick up programmatically-set names,
	// so use a roundabout getElementsByName test
	support.getById = assert( function( el ) {
		docElem.appendChild( el ).id = expando;
		return !document.getElementsByName || !document.getElementsByName( expando ).length;
	} );

	// ID filter and find
	if ( support.getById ) {
		Expr.filter[ "ID" ] = function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				return elem.getAttribute( "id" ) === attrId;
			};
		};
		Expr.find[ "ID" ] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var elem = context.getElementById( id );
				return elem ? [ elem ] : [];
			}
		};
	} else {
		Expr.filter[ "ID" ] =  function( id ) {
			var attrId = id.replace( runescape, funescape );
			return function( elem ) {
				var node = typeof elem.getAttributeNode !== "undefined" &&
					elem.getAttributeNode( "id" );
				return node && node.value === attrId;
			};
		};

		// Support: IE 6 - 7 only
		// getElementById is not reliable as a find shortcut
		Expr.find[ "ID" ] = function( id, context ) {
			if ( typeof context.getElementById !== "undefined" && documentIsHTML ) {
				var node, i, elems,
					elem = context.getElementById( id );

				if ( elem ) {

					// Verify the id attribute
					node = elem.getAttributeNode( "id" );
					if ( node && node.value === id ) {
						return [ elem ];
					}

					// Fall back on getElementsByName
					elems = context.getElementsByName( id );
					i = 0;
					while ( ( elem = elems[ i++ ] ) ) {
						node = elem.getAttributeNode( "id" );
						if ( node && node.value === id ) {
							return [ elem ];
						}
					}
				}

				return [];
			}
		};
	}

	// Tag
	Expr.find[ "TAG" ] = support.getElementsByTagName ?
		function( tag, context ) {
			if ( typeof context.getElementsByTagName !== "undefined" ) {
				return context.getElementsByTagName( tag );

			// DocumentFragment nodes don't have gEBTN
			} else if ( support.qsa ) {
				return context.querySelectorAll( tag );
			}
		} :

		function( tag, context ) {
			var elem,
				tmp = [],
				i = 0,

				// By happy coincidence, a (broken) gEBTN appears on DocumentFragment nodes too
				results = context.getElementsByTagName( tag );

			// Filter out possible comments
			if ( tag === "*" ) {
				while ( ( elem = results[ i++ ] ) ) {
					if ( elem.nodeType === 1 ) {
						tmp.push( elem );
					}
				}

				return tmp;
			}
			return results;
		};

	// Class
	Expr.find[ "CLASS" ] = support.getElementsByClassName && function( className, context ) {
		if ( typeof context.getElementsByClassName !== "undefined" && documentIsHTML ) {
			return context.getElementsByClassName( className );
		}
	};

	/* QSA/matchesSelector
	---------------------------------------------------------------------- */

	// QSA and matchesSelector support

	// matchesSelector(:active) reports false when true (IE9/Opera 11.5)
	rbuggyMatches = [];

	// qSa(:focus) reports false when true (Chrome 21)
	// We allow this because of a bug in IE8/9 that throws an error
	// whenever `document.activeElement` is accessed on an iframe
	// So, we allow :focus to pass through QSA all the time to avoid the IE error
	// See https://bugs.jquery.com/ticket/13378
	rbuggyQSA = [];

	if ( ( support.qsa = rnative.test( document.querySelectorAll ) ) ) {

		// Build QSA regex
		// Regex strategy adopted from Diego Perini
		assert( function( el ) {

			var input;

			// Select is set to empty string on purpose
			// This is to test IE's treatment of not explicitly
			// setting a boolean content attribute,
			// since its presence should be enough
			// https://bugs.jquery.com/ticket/12359
			docElem.appendChild( el ).innerHTML = "<a id='" + expando + "'></a>" +
				"<select id='" + expando + "-\r\\' msallowcapture=''>" +
				"<option selected=''></option></select>";

			// Support: IE8, Opera 11-12.16
			// Nothing should be selected when empty strings follow ^= or $= or *=
			// The test attribute must be unknown in Opera but "safe" for WinRT
			// https://msdn.microsoft.com/en-us/library/ie/hh465388.aspx#attribute_section
			if ( el.querySelectorAll( "[msallowcapture^='']" ).length ) {
				rbuggyQSA.push( "[*^$]=" + whitespace + "*(?:''|\"\")" );
			}

			// Support: IE8
			// Boolean attributes and "value" are not treated correctly
			if ( !el.querySelectorAll( "[selected]" ).length ) {
				rbuggyQSA.push( "\\[" + whitespace + "*(?:value|" + booleans + ")" );
			}

			// Support: Chrome<29, Android<4.4, Safari<7.0+, iOS<7.0+, PhantomJS<1.9.8+
			if ( !el.querySelectorAll( "[id~=" + expando + "-]" ).length ) {
				rbuggyQSA.push( "~=" );
			}

			// Support: IE 11+, Edge 15 - 18+
			// IE 11/Edge don't find elements on a `[name='']` query in some cases.
			// Adding a temporary attribute to the document before the selection works
			// around the issue.
			// Interestingly, IE 10 & older don't seem to have the issue.
			input = document.createElement( "input" );
			input.setAttribute( "name", "" );
			el.appendChild( input );
			if ( !el.querySelectorAll( "[name='']" ).length ) {
				rbuggyQSA.push( "\\[" + whitespace + "*name" + whitespace + "*=" +
					whitespace + "*(?:''|\"\")" );
			}

			// Webkit/Opera - :checked should return selected option elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			// IE8 throws error here and will not see later tests
			if ( !el.querySelectorAll( ":checked" ).length ) {
				rbuggyQSA.push( ":checked" );
			}

			// Support: Safari 8+, iOS 8+
			// https://bugs.webkit.org/show_bug.cgi?id=136851
			// In-page `selector#id sibling-combinator selector` fails
			if ( !el.querySelectorAll( "a#" + expando + "+*" ).length ) {
				rbuggyQSA.push( ".#.+[+~]" );
			}

			// Support: Firefox <=3.6 - 5 only
			// Old Firefox doesn't throw on a badly-escaped identifier.
			el.querySelectorAll( "\\\f" );
			rbuggyQSA.push( "[\\r\\n\\f]" );
		} );

		assert( function( el ) {
			el.innerHTML = "<a href='' disabled='disabled'></a>" +
				"<select disabled='disabled'><option/></select>";

			// Support: Windows 8 Native Apps
			// The type and name attributes are restricted during .innerHTML assignment
			var input = document.createElement( "input" );
			input.setAttribute( "type", "hidden" );
			el.appendChild( input ).setAttribute( "name", "D" );

			// Support: IE8
			// Enforce case-sensitivity of name attribute
			if ( el.querySelectorAll( "[name=d]" ).length ) {
				rbuggyQSA.push( "name" + whitespace + "*[*^$|!~]?=" );
			}

			// FF 3.5 - :enabled/:disabled and hidden elements (hidden elements are still enabled)
			// IE8 throws error here and will not see later tests
			if ( el.querySelectorAll( ":enabled" ).length !== 2 ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Support: IE9-11+
			// IE's :disabled selector does not pick up the children of disabled fieldsets
			docElem.appendChild( el ).disabled = true;
			if ( el.querySelectorAll( ":disabled" ).length !== 2 ) {
				rbuggyQSA.push( ":enabled", ":disabled" );
			}

			// Support: Opera 10 - 11 only
			// Opera 10-11 does not throw on post-comma invalid pseudos
			el.querySelectorAll( "*,:x" );
			rbuggyQSA.push( ",.*:" );
		} );
	}

	if ( ( support.matchesSelector = rnative.test( ( matches = docElem.matches ||
		docElem.webkitMatchesSelector ||
		docElem.mozMatchesSelector ||
		docElem.oMatchesSelector ||
		docElem.msMatchesSelector ) ) ) ) {

		assert( function( el ) {

			// Check to see if it's possible to do matchesSelector
			// on a disconnected node (IE 9)
			support.disconnectedMatch = matches.call( el, "*" );

			// This should fail with an exception
			// Gecko does not error, returns false instead
			matches.call( el, "[s!='']:x" );
			rbuggyMatches.push( "!=", pseudos );
		} );
	}

	rbuggyQSA = rbuggyQSA.length && new RegExp( rbuggyQSA.join( "|" ) );
	rbuggyMatches = rbuggyMatches.length && new RegExp( rbuggyMatches.join( "|" ) );

	/* Contains
	---------------------------------------------------------------------- */
	hasCompare = rnative.test( docElem.compareDocumentPosition );

	// Element contains another
	// Purposefully self-exclusive
	// As in, an element does not contain itself
	contains = hasCompare || rnative.test( docElem.contains ) ?
		function( a, b ) {
			var adown = a.nodeType === 9 ? a.documentElement : a,
				bup = b && b.parentNode;
			return a === bup || !!( bup && bup.nodeType === 1 && (
				adown.contains ?
					adown.contains( bup ) :
					a.compareDocumentPosition && a.compareDocumentPosition( bup ) & 16
			) );
		} :
		function( a, b ) {
			if ( b ) {
				while ( ( b = b.parentNode ) ) {
					if ( b === a ) {
						return true;
					}
				}
			}
			return false;
		};

	/* Sorting
	---------------------------------------------------------------------- */

	// Document order sorting
	sortOrder = hasCompare ?
	function( a, b ) {

		// Flag for duplicate removal
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		// Sort on method existence if only one input has compareDocumentPosition
		var compare = !a.compareDocumentPosition - !b.compareDocumentPosition;
		if ( compare ) {
			return compare;
		}

		// Calculate position if both inputs belong to the same document
		// Support: IE 11+, Edge 17 - 18+
		// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
		// two documents; shallow comparisons work.
		// eslint-disable-next-line eqeqeq
		compare = ( a.ownerDocument || a ) == ( b.ownerDocument || b ) ?
			a.compareDocumentPosition( b ) :

			// Otherwise we know they are disconnected
			1;

		// Disconnected nodes
		if ( compare & 1 ||
			( !support.sortDetached && b.compareDocumentPosition( a ) === compare ) ) {

			// Choose the first element that is related to our preferred document
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ( a == document || a.ownerDocument == preferredDoc &&
				contains( preferredDoc, a ) ) {
				return -1;
			}

			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			// eslint-disable-next-line eqeqeq
			if ( b == document || b.ownerDocument == preferredDoc &&
				contains( preferredDoc, b ) ) {
				return 1;
			}

			// Maintain original order
			return sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;
		}

		return compare & 4 ? -1 : 1;
	} :
	function( a, b ) {

		// Exit early if the nodes are identical
		if ( a === b ) {
			hasDuplicate = true;
			return 0;
		}

		var cur,
			i = 0,
			aup = a.parentNode,
			bup = b.parentNode,
			ap = [ a ],
			bp = [ b ];

		// Parentless nodes are either documents or disconnected
		if ( !aup || !bup ) {

			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			/* eslint-disable eqeqeq */
			return a == document ? -1 :
				b == document ? 1 :
				/* eslint-enable eqeqeq */
				aup ? -1 :
				bup ? 1 :
				sortInput ?
				( indexOf( sortInput, a ) - indexOf( sortInput, b ) ) :
				0;

		// If the nodes are siblings, we can do a quick check
		} else if ( aup === bup ) {
			return siblingCheck( a, b );
		}

		// Otherwise we need full lists of their ancestors for comparison
		cur = a;
		while ( ( cur = cur.parentNode ) ) {
			ap.unshift( cur );
		}
		cur = b;
		while ( ( cur = cur.parentNode ) ) {
			bp.unshift( cur );
		}

		// Walk down the tree looking for a discrepancy
		while ( ap[ i ] === bp[ i ] ) {
			i++;
		}

		return i ?

			// Do a sibling check if the nodes have a common ancestor
			siblingCheck( ap[ i ], bp[ i ] ) :

			// Otherwise nodes in our document sort first
			// Support: IE 11+, Edge 17 - 18+
			// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
			// two documents; shallow comparisons work.
			/* eslint-disable eqeqeq */
			ap[ i ] == preferredDoc ? -1 :
			bp[ i ] == preferredDoc ? 1 :
			/* eslint-enable eqeqeq */
			0;
	};

	return document;
};

Sizzle.matches = function( expr, elements ) {
	return Sizzle( expr, null, null, elements );
};

Sizzle.matchesSelector = function( elem, expr ) {
	setDocument( elem );

	if ( support.matchesSelector && documentIsHTML &&
		!nonnativeSelectorCache[ expr + " " ] &&
		( !rbuggyMatches || !rbuggyMatches.test( expr ) ) &&
		( !rbuggyQSA     || !rbuggyQSA.test( expr ) ) ) {

		try {
			var ret = matches.call( elem, expr );

			// IE 9's matchesSelector returns false on disconnected nodes
			if ( ret || support.disconnectedMatch ||

				// As well, disconnected nodes are said to be in a document
				// fragment in IE 9
				elem.document && elem.document.nodeType !== 11 ) {
				return ret;
			}
		} catch ( e ) {
			nonnativeSelectorCache( expr, true );
		}
	}

	return Sizzle( expr, document, null, [ elem ] ).length > 0;
};

Sizzle.contains = function( context, elem ) {

	// Set document vars if needed
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( ( context.ownerDocument || context ) != document ) {
		setDocument( context );
	}
	return contains( context, elem );
};

Sizzle.attr = function( elem, name ) {

	// Set document vars if needed
	// Support: IE 11+, Edge 17 - 18+
	// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
	// two documents; shallow comparisons work.
	// eslint-disable-next-line eqeqeq
	if ( ( elem.ownerDocument || elem ) != document ) {
		setDocument( elem );
	}

	var fn = Expr.attrHandle[ name.toLowerCase() ],

		// Don't get fooled by Object.prototype properties (jQuery #13807)
		val = fn && hasOwn.call( Expr.attrHandle, name.toLowerCase() ) ?
			fn( elem, name, !documentIsHTML ) :
			undefined;

	return val !== undefined ?
		val :
		support.attributes || !documentIsHTML ?
			elem.getAttribute( name ) :
			( val = elem.getAttributeNode( name ) ) && val.specified ?
				val.value :
				null;
};

Sizzle.escape = function( sel ) {
	return ( sel + "" ).replace( rcssescape, fcssescape );
};

Sizzle.error = function( msg ) {
	throw new Error( "Syntax error, unrecognized expression: " + msg );
};

/**
 * Document sorting and removing duplicates
 * @param {ArrayLike} results
 */
Sizzle.uniqueSort = function( results ) {
	var elem,
		duplicates = [],
		j = 0,
		i = 0;

	// Unless we *know* we can detect duplicates, assume their presence
	hasDuplicate = !support.detectDuplicates;
	sortInput = !support.sortStable && results.slice( 0 );
	results.sort( sortOrder );

	if ( hasDuplicate ) {
		while ( ( elem = results[ i++ ] ) ) {
			if ( elem === results[ i ] ) {
				j = duplicates.push( i );
			}
		}
		while ( j-- ) {
			results.splice( duplicates[ j ], 1 );
		}
	}

	// Clear input after sorting to release objects
	// See https://github.com/jquery/sizzle/pull/225
	sortInput = null;

	return results;
};

/**
 * Utility function for retrieving the text value of an array of DOM nodes
 * @param {Array|Element} elem
 */
getText = Sizzle.getText = function( elem ) {
	var node,
		ret = "",
		i = 0,
		nodeType = elem.nodeType;

	if ( !nodeType ) {

		// If no nodeType, this is expected to be an array
		while ( ( node = elem[ i++ ] ) ) {

			// Do not traverse comment nodes
			ret += getText( node );
		}
	} else if ( nodeType === 1 || nodeType === 9 || nodeType === 11 ) {

		// Use textContent for elements
		// innerText usage removed for consistency of new lines (jQuery #11153)
		if ( typeof elem.textContent === "string" ) {
			return elem.textContent;
		} else {

			// Traverse its children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				ret += getText( elem );
			}
		}
	} else if ( nodeType === 3 || nodeType === 4 ) {
		return elem.nodeValue;
	}

	// Do not include comment or processing instruction nodes

	return ret;
};

Expr = Sizzle.selectors = {

	// Can be adjusted by the user
	cacheLength: 50,

	createPseudo: markFunction,

	match: matchExpr,

	attrHandle: {},

	find: {},

	relative: {
		">": { dir: "parentNode", first: true },
		" ": { dir: "parentNode" },
		"+": { dir: "previousSibling", first: true },
		"~": { dir: "previousSibling" }
	},

	preFilter: {
		"ATTR": function( match ) {
			match[ 1 ] = match[ 1 ].replace( runescape, funescape );

			// Move the given value to match[3] whether quoted or unquoted
			match[ 3 ] = ( match[ 3 ] || match[ 4 ] ||
				match[ 5 ] || "" ).replace( runescape, funescape );

			if ( match[ 2 ] === "~=" ) {
				match[ 3 ] = " " + match[ 3 ] + " ";
			}

			return match.slice( 0, 4 );
		},

		"CHILD": function( match ) {

			/* matches from matchExpr["CHILD"]
				1 type (only|nth|...)
				2 what (child|of-type)
				3 argument (even|odd|\d*|\d*n([+-]\d+)?|...)
				4 xn-component of xn+y argument ([+-]?\d*n|)
				5 sign of xn-component
				6 x of xn-component
				7 sign of y-component
				8 y of y-component
			*/
			match[ 1 ] = match[ 1 ].toLowerCase();

			if ( match[ 1 ].slice( 0, 3 ) === "nth" ) {

				// nth-* requires argument
				if ( !match[ 3 ] ) {
					Sizzle.error( match[ 0 ] );
				}

				// numeric x and y parameters for Expr.filter.CHILD
				// remember that false/true cast respectively to 0/1
				match[ 4 ] = +( match[ 4 ] ?
					match[ 5 ] + ( match[ 6 ] || 1 ) :
					2 * ( match[ 3 ] === "even" || match[ 3 ] === "odd" ) );
				match[ 5 ] = +( ( match[ 7 ] + match[ 8 ] ) || match[ 3 ] === "odd" );

				// other types prohibit arguments
			} else if ( match[ 3 ] ) {
				Sizzle.error( match[ 0 ] );
			}

			return match;
		},

		"PSEUDO": function( match ) {
			var excess,
				unquoted = !match[ 6 ] && match[ 2 ];

			if ( matchExpr[ "CHILD" ].test( match[ 0 ] ) ) {
				return null;
			}

			// Accept quoted arguments as-is
			if ( match[ 3 ] ) {
				match[ 2 ] = match[ 4 ] || match[ 5 ] || "";

			// Strip excess characters from unquoted arguments
			} else if ( unquoted && rpseudo.test( unquoted ) &&

				// Get excess from tokenize (recursively)
				( excess = tokenize( unquoted, true ) ) &&

				// advance to the next closing parenthesis
				( excess = unquoted.indexOf( ")", unquoted.length - excess ) - unquoted.length ) ) {

				// excess is a negative index
				match[ 0 ] = match[ 0 ].slice( 0, excess );
				match[ 2 ] = unquoted.slice( 0, excess );
			}

			// Return only captures needed by the pseudo filter method (type and argument)
			return match.slice( 0, 3 );
		}
	},

	filter: {

		"TAG": function( nodeNameSelector ) {
			var nodeName = nodeNameSelector.replace( runescape, funescape ).toLowerCase();
			return nodeNameSelector === "*" ?
				function() {
					return true;
				} :
				function( elem ) {
					return elem.nodeName && elem.nodeName.toLowerCase() === nodeName;
				};
		},

		"CLASS": function( className ) {
			var pattern = classCache[ className + " " ];

			return pattern ||
				( pattern = new RegExp( "(^|" + whitespace +
					")" + className + "(" + whitespace + "|$)" ) ) && classCache(
						className, function( elem ) {
							return pattern.test(
								typeof elem.className === "string" && elem.className ||
								typeof elem.getAttribute !== "undefined" &&
									elem.getAttribute( "class" ) ||
								""
							);
				} );
		},

		"ATTR": function( name, operator, check ) {
			return function( elem ) {
				var result = Sizzle.attr( elem, name );

				if ( result == null ) {
					return operator === "!=";
				}
				if ( !operator ) {
					return true;
				}

				result += "";

				/* eslint-disable max-len */

				return operator === "=" ? result === check :
					operator === "!=" ? result !== check :
					operator === "^=" ? check && result.indexOf( check ) === 0 :
					operator === "*=" ? check && result.indexOf( check ) > -1 :
					operator === "$=" ? check && result.slice( -check.length ) === check :
					operator === "~=" ? ( " " + result.replace( rwhitespace, " " ) + " " ).indexOf( check ) > -1 :
					operator === "|=" ? result === check || result.slice( 0, check.length + 1 ) === check + "-" :
					false;
				/* eslint-enable max-len */

			};
		},

		"CHILD": function( type, what, _argument, first, last ) {
			var simple = type.slice( 0, 3 ) !== "nth",
				forward = type.slice( -4 ) !== "last",
				ofType = what === "of-type";

			return first === 1 && last === 0 ?

				// Shortcut for :nth-*(n)
				function( elem ) {
					return !!elem.parentNode;
				} :

				function( elem, _context, xml ) {
					var cache, uniqueCache, outerCache, node, nodeIndex, start,
						dir = simple !== forward ? "nextSibling" : "previousSibling",
						parent = elem.parentNode,
						name = ofType && elem.nodeName.toLowerCase(),
						useCache = !xml && !ofType,
						diff = false;

					if ( parent ) {

						// :(first|last|only)-(child|of-type)
						if ( simple ) {
							while ( dir ) {
								node = elem;
								while ( ( node = node[ dir ] ) ) {
									if ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) {

										return false;
									}
								}

								// Reverse direction for :only-* (if we haven't yet done so)
								start = dir = type === "only" && !start && "nextSibling";
							}
							return true;
						}

						start = [ forward ? parent.firstChild : parent.lastChild ];

						// non-xml :nth-child(...) stores cache data on `parent`
						if ( forward && useCache ) {

							// Seek `elem` from a previously-cached index

							// ...in a gzip-friendly way
							node = parent;
							outerCache = node[ expando ] || ( node[ expando ] = {} );

							// Support: IE <9 only
							// Defend against cloned attroperties (jQuery gh-1709)
							uniqueCache = outerCache[ node.uniqueID ] ||
								( outerCache[ node.uniqueID ] = {} );

							cache = uniqueCache[ type ] || [];
							nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
							diff = nodeIndex && cache[ 2 ];
							node = nodeIndex && parent.childNodes[ nodeIndex ];

							while ( ( node = ++nodeIndex && node && node[ dir ] ||

								// Fallback to seeking `elem` from the start
								( diff = nodeIndex = 0 ) || start.pop() ) ) {

								// When found, cache indexes on `parent` and break
								if ( node.nodeType === 1 && ++diff && node === elem ) {
									uniqueCache[ type ] = [ dirruns, nodeIndex, diff ];
									break;
								}
							}

						} else {

							// Use previously-cached element index if available
							if ( useCache ) {

								// ...in a gzip-friendly way
								node = elem;
								outerCache = node[ expando ] || ( node[ expando ] = {} );

								// Support: IE <9 only
								// Defend against cloned attroperties (jQuery gh-1709)
								uniqueCache = outerCache[ node.uniqueID ] ||
									( outerCache[ node.uniqueID ] = {} );

								cache = uniqueCache[ type ] || [];
								nodeIndex = cache[ 0 ] === dirruns && cache[ 1 ];
								diff = nodeIndex;
							}

							// xml :nth-child(...)
							// or :nth-last-child(...) or :nth(-last)?-of-type(...)
							if ( diff === false ) {

								// Use the same loop as above to seek `elem` from the start
								while ( ( node = ++nodeIndex && node && node[ dir ] ||
									( diff = nodeIndex = 0 ) || start.pop() ) ) {

									if ( ( ofType ?
										node.nodeName.toLowerCase() === name :
										node.nodeType === 1 ) &&
										++diff ) {

										// Cache the index of each encountered element
										if ( useCache ) {
											outerCache = node[ expando ] ||
												( node[ expando ] = {} );

											// Support: IE <9 only
											// Defend against cloned attroperties (jQuery gh-1709)
											uniqueCache = outerCache[ node.uniqueID ] ||
												( outerCache[ node.uniqueID ] = {} );

											uniqueCache[ type ] = [ dirruns, diff ];
										}

										if ( node === elem ) {
											break;
										}
									}
								}
							}
						}

						// Incorporate the offset, then check against cycle size
						diff -= last;
						return diff === first || ( diff % first === 0 && diff / first >= 0 );
					}
				};
		},

		"PSEUDO": function( pseudo, argument ) {

			// pseudo-class names are case-insensitive
			// http://www.w3.org/TR/selectors/#pseudo-classes
			// Prioritize by case sensitivity in case custom pseudos are added with uppercase letters
			// Remember that setFilters inherits from pseudos
			var args,
				fn = Expr.pseudos[ pseudo ] || Expr.setFilters[ pseudo.toLowerCase() ] ||
					Sizzle.error( "unsupported pseudo: " + pseudo );

			// The user may use createPseudo to indicate that
			// arguments are needed to create the filter function
			// just as Sizzle does
			if ( fn[ expando ] ) {
				return fn( argument );
			}

			// But maintain support for old signatures
			if ( fn.length > 1 ) {
				args = [ pseudo, pseudo, "", argument ];
				return Expr.setFilters.hasOwnProperty( pseudo.toLowerCase() ) ?
					markFunction( function( seed, matches ) {
						var idx,
							matched = fn( seed, argument ),
							i = matched.length;
						while ( i-- ) {
							idx = indexOf( seed, matched[ i ] );
							seed[ idx ] = !( matches[ idx ] = matched[ i ] );
						}
					} ) :
					function( elem ) {
						return fn( elem, 0, args );
					};
			}

			return fn;
		}
	},

	pseudos: {

		// Potentially complex pseudos
		"not": markFunction( function( selector ) {

			// Trim the selector passed to compile
			// to avoid treating leading and trailing
			// spaces as combinators
			var input = [],
				results = [],
				matcher = compile( selector.replace( rtrim, "$1" ) );

			return matcher[ expando ] ?
				markFunction( function( seed, matches, _context, xml ) {
					var elem,
						unmatched = matcher( seed, null, xml, [] ),
						i = seed.length;

					// Match elements unmatched by `matcher`
					while ( i-- ) {
						if ( ( elem = unmatched[ i ] ) ) {
							seed[ i ] = !( matches[ i ] = elem );
						}
					}
				} ) :
				function( elem, _context, xml ) {
					input[ 0 ] = elem;
					matcher( input, null, xml, results );

					// Don't keep the element (issue #299)
					input[ 0 ] = null;
					return !results.pop();
				};
		} ),

		"has": markFunction( function( selector ) {
			return function( elem ) {
				return Sizzle( selector, elem ).length > 0;
			};
		} ),

		"contains": markFunction( function( text ) {
			text = text.replace( runescape, funescape );
			return function( elem ) {
				return ( elem.textContent || getText( elem ) ).indexOf( text ) > -1;
			};
		} ),

		// "Whether an element is represented by a :lang() selector
		// is based solely on the element's language value
		// being equal to the identifier C,
		// or beginning with the identifier C immediately followed by "-".
		// The matching of C against the element's language value is performed case-insensitively.
		// The identifier C does not have to be a valid language name."
		// http://www.w3.org/TR/selectors/#lang-pseudo
		"lang": markFunction( function( lang ) {

			// lang value must be a valid identifier
			if ( !ridentifier.test( lang || "" ) ) {
				Sizzle.error( "unsupported lang: " + lang );
			}
			lang = lang.replace( runescape, funescape ).toLowerCase();
			return function( elem ) {
				var elemLang;
				do {
					if ( ( elemLang = documentIsHTML ?
						elem.lang :
						elem.getAttribute( "xml:lang" ) || elem.getAttribute( "lang" ) ) ) {

						elemLang = elemLang.toLowerCase();
						return elemLang === lang || elemLang.indexOf( lang + "-" ) === 0;
					}
				} while ( ( elem = elem.parentNode ) && elem.nodeType === 1 );
				return false;
			};
		} ),

		// Miscellaneous
		"target": function( elem ) {
			var hash = window.location && window.location.hash;
			return hash && hash.slice( 1 ) === elem.id;
		},

		"root": function( elem ) {
			return elem === docElem;
		},

		"focus": function( elem ) {
			return elem === document.activeElement &&
				( !document.hasFocus || document.hasFocus() ) &&
				!!( elem.type || elem.href || ~elem.tabIndex );
		},

		// Boolean properties
		"enabled": createDisabledPseudo( false ),
		"disabled": createDisabledPseudo( true ),

		"checked": function( elem ) {

			// In CSS3, :checked should return both checked and selected elements
			// http://www.w3.org/TR/2011/REC-css3-selectors-20110929/#checked
			var nodeName = elem.nodeName.toLowerCase();
			return ( nodeName === "input" && !!elem.checked ) ||
				( nodeName === "option" && !!elem.selected );
		},

		"selected": function( elem ) {

			// Accessing this property makes selected-by-default
			// options in Safari work properly
			if ( elem.parentNode ) {
				// eslint-disable-next-line no-unused-expressions
				elem.parentNode.selectedIndex;
			}

			return elem.selected === true;
		},

		// Contents
		"empty": function( elem ) {

			// http://www.w3.org/TR/selectors/#empty-pseudo
			// :empty is negated by element (1) or content nodes (text: 3; cdata: 4; entity ref: 5),
			//   but not by others (comment: 8; processing instruction: 7; etc.)
			// nodeType < 6 works because attributes (2) do not appear as children
			for ( elem = elem.firstChild; elem; elem = elem.nextSibling ) {
				if ( elem.nodeType < 6 ) {
					return false;
				}
			}
			return true;
		},

		"parent": function( elem ) {
			return !Expr.pseudos[ "empty" ]( elem );
		},

		// Element/input types
		"header": function( elem ) {
			return rheader.test( elem.nodeName );
		},

		"input": function( elem ) {
			return rinputs.test( elem.nodeName );
		},

		"button": function( elem ) {
			var name = elem.nodeName.toLowerCase();
			return name === "input" && elem.type === "button" || name === "button";
		},

		"text": function( elem ) {
			var attr;
			return elem.nodeName.toLowerCase() === "input" &&
				elem.type === "text" &&

				// Support: IE<8
				// New HTML5 attribute values (e.g., "search") appear with elem.type === "text"
				( ( attr = elem.getAttribute( "type" ) ) == null ||
					attr.toLowerCase() === "text" );
		},

		// Position-in-collection
		"first": createPositionalPseudo( function() {
			return [ 0 ];
		} ),

		"last": createPositionalPseudo( function( _matchIndexes, length ) {
			return [ length - 1 ];
		} ),

		"eq": createPositionalPseudo( function( _matchIndexes, length, argument ) {
			return [ argument < 0 ? argument + length : argument ];
		} ),

		"even": createPositionalPseudo( function( matchIndexes, length ) {
			var i = 0;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		"odd": createPositionalPseudo( function( matchIndexes, length ) {
			var i = 1;
			for ( ; i < length; i += 2 ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		"lt": createPositionalPseudo( function( matchIndexes, length, argument ) {
			var i = argument < 0 ?
				argument + length :
				argument > length ?
					length :
					argument;
			for ( ; --i >= 0; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} ),

		"gt": createPositionalPseudo( function( matchIndexes, length, argument ) {
			var i = argument < 0 ? argument + length : argument;
			for ( ; ++i < length; ) {
				matchIndexes.push( i );
			}
			return matchIndexes;
		} )
	}
};

Expr.pseudos[ "nth" ] = Expr.pseudos[ "eq" ];

// Add button/input type pseudos
for ( i in { radio: true, checkbox: true, file: true, password: true, image: true } ) {
	Expr.pseudos[ i ] = createInputPseudo( i );
}
for ( i in { submit: true, reset: true } ) {
	Expr.pseudos[ i ] = createButtonPseudo( i );
}

// Easy API for creating new setFilters
function setFilters() {}
setFilters.prototype = Expr.filters = Expr.pseudos;
Expr.setFilters = new setFilters();

tokenize = Sizzle.tokenize = function( selector, parseOnly ) {
	var matched, match, tokens, type,
		soFar, groups, preFilters,
		cached = tokenCache[ selector + " " ];

	if ( cached ) {
		return parseOnly ? 0 : cached.slice( 0 );
	}

	soFar = selector;
	groups = [];
	preFilters = Expr.preFilter;

	while ( soFar ) {

		// Comma and first run
		if ( !matched || ( match = rcomma.exec( soFar ) ) ) {
			if ( match ) {

				// Don't consume trailing commas as valid
				soFar = soFar.slice( match[ 0 ].length ) || soFar;
			}
			groups.push( ( tokens = [] ) );
		}

		matched = false;

		// Combinators
		if ( ( match = rcombinators.exec( soFar ) ) ) {
			matched = match.shift();
			tokens.push( {
				value: matched,

				// Cast descendant combinators to space
				type: match[ 0 ].replace( rtrim, " " )
			} );
			soFar = soFar.slice( matched.length );
		}

		// Filters
		for ( type in Expr.filter ) {
			if ( ( match = matchExpr[ type ].exec( soFar ) ) && ( !preFilters[ type ] ||
				( match = preFilters[ type ]( match ) ) ) ) {
				matched = match.shift();
				tokens.push( {
					value: matched,
					type: type,
					matches: match
				} );
				soFar = soFar.slice( matched.length );
			}
		}

		if ( !matched ) {
			break;
		}
	}

	// Return the length of the invalid excess
	// if we're just parsing
	// Otherwise, throw an error or return tokens
	return parseOnly ?
		soFar.length :
		soFar ?
			Sizzle.error( selector ) :

			// Cache the tokens
			tokenCache( selector, groups ).slice( 0 );
};

function toSelector( tokens ) {
	var i = 0,
		len = tokens.length,
		selector = "";
	for ( ; i < len; i++ ) {
		selector += tokens[ i ].value;
	}
	return selector;
}

function addCombinator( matcher, combinator, base ) {
	var dir = combinator.dir,
		skip = combinator.next,
		key = skip || dir,
		checkNonElements = base && key === "parentNode",
		doneName = done++;

	return combinator.first ?

		// Check against closest ancestor/preceding element
		function( elem, context, xml ) {
			while ( ( elem = elem[ dir ] ) ) {
				if ( elem.nodeType === 1 || checkNonElements ) {
					return matcher( elem, context, xml );
				}
			}
			return false;
		} :

		// Check against all ancestor/preceding elements
		function( elem, context, xml ) {
			var oldCache, uniqueCache, outerCache,
				newCache = [ dirruns, doneName ];

			// We can't set arbitrary data on XML nodes, so they don't benefit from combinator caching
			if ( xml ) {
				while ( ( elem = elem[ dir ] ) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						if ( matcher( elem, context, xml ) ) {
							return true;
						}
					}
				}
			} else {
				while ( ( elem = elem[ dir ] ) ) {
					if ( elem.nodeType === 1 || checkNonElements ) {
						outerCache = elem[ expando ] || ( elem[ expando ] = {} );

						// Support: IE <9 only
						// Defend against cloned attroperties (jQuery gh-1709)
						uniqueCache = outerCache[ elem.uniqueID ] ||
							( outerCache[ elem.uniqueID ] = {} );

						if ( skip && skip === elem.nodeName.toLowerCase() ) {
							elem = elem[ dir ] || elem;
						} else if ( ( oldCache = uniqueCache[ key ] ) &&
							oldCache[ 0 ] === dirruns && oldCache[ 1 ] === doneName ) {

							// Assign to newCache so results back-propagate to previous elements
							return ( newCache[ 2 ] = oldCache[ 2 ] );
						} else {

							// Reuse newcache so results back-propagate to previous elements
							uniqueCache[ key ] = newCache;

							// A match means we're done; a fail means we have to keep checking
							if ( ( newCache[ 2 ] = matcher( elem, context, xml ) ) ) {
								return true;
							}
						}
					}
				}
			}
			return false;
		};
}

function elementMatcher( matchers ) {
	return matchers.length > 1 ?
		function( elem, context, xml ) {
			var i = matchers.length;
			while ( i-- ) {
				if ( !matchers[ i ]( elem, context, xml ) ) {
					return false;
				}
			}
			return true;
		} :
		matchers[ 0 ];
}

function multipleContexts( selector, contexts, results ) {
	var i = 0,
		len = contexts.length;
	for ( ; i < len; i++ ) {
		Sizzle( selector, contexts[ i ], results );
	}
	return results;
}

function condense( unmatched, map, filter, context, xml ) {
	var elem,
		newUnmatched = [],
		i = 0,
		len = unmatched.length,
		mapped = map != null;

	for ( ; i < len; i++ ) {
		if ( ( elem = unmatched[ i ] ) ) {
			if ( !filter || filter( elem, context, xml ) ) {
				newUnmatched.push( elem );
				if ( mapped ) {
					map.push( i );
				}
			}
		}
	}

	return newUnmatched;
}

function setMatcher( preFilter, selector, matcher, postFilter, postFinder, postSelector ) {
	if ( postFilter && !postFilter[ expando ] ) {
		postFilter = setMatcher( postFilter );
	}
	if ( postFinder && !postFinder[ expando ] ) {
		postFinder = setMatcher( postFinder, postSelector );
	}
	return markFunction( function( seed, results, context, xml ) {
		var temp, i, elem,
			preMap = [],
			postMap = [],
			preexisting = results.length,

			// Get initial elements from seed or context
			elems = seed || multipleContexts(
				selector || "*",
				context.nodeType ? [ context ] : context,
				[]
			),

			// Prefilter to get matcher input, preserving a map for seed-results synchronization
			matcherIn = preFilter && ( seed || !selector ) ?
				condense( elems, preMap, preFilter, context, xml ) :
				elems,

			matcherOut = matcher ?

				// If we have a postFinder, or filtered seed, or non-seed postFilter or preexisting results,
				postFinder || ( seed ? preFilter : preexisting || postFilter ) ?

					// ...intermediate processing is necessary
					[] :

					// ...otherwise use results directly
					results :
				matcherIn;

		// Find primary matches
		if ( matcher ) {
			matcher( matcherIn, matcherOut, context, xml );
		}

		// Apply postFilter
		if ( postFilter ) {
			temp = condense( matcherOut, postMap );
			postFilter( temp, [], context, xml );

			// Un-match failing elements by moving them back to matcherIn
			i = temp.length;
			while ( i-- ) {
				if ( ( elem = temp[ i ] ) ) {
					matcherOut[ postMap[ i ] ] = !( matcherIn[ postMap[ i ] ] = elem );
				}
			}
		}

		if ( seed ) {
			if ( postFinder || preFilter ) {
				if ( postFinder ) {

					// Get the final matcherOut by condensing this intermediate into postFinder contexts
					temp = [];
					i = matcherOut.length;
					while ( i-- ) {
						if ( ( elem = matcherOut[ i ] ) ) {

							// Restore matcherIn since elem is not yet a final match
							temp.push( ( matcherIn[ i ] = elem ) );
						}
					}
					postFinder( null, ( matcherOut = [] ), temp, xml );
				}

				// Move matched elements from seed to results to keep them synchronized
				i = matcherOut.length;
				while ( i-- ) {
					if ( ( elem = matcherOut[ i ] ) &&
						( temp = postFinder ? indexOf( seed, elem ) : preMap[ i ] ) > -1 ) {

						seed[ temp ] = !( results[ temp ] = elem );
					}
				}
			}

		// Add elements to results, through postFinder if defined
		} else {
			matcherOut = condense(
				matcherOut === results ?
					matcherOut.splice( preexisting, matcherOut.length ) :
					matcherOut
			);
			if ( postFinder ) {
				postFinder( null, results, matcherOut, xml );
			} else {
				push.apply( results, matcherOut );
			}
		}
	} );
}

function matcherFromTokens( tokens ) {
	var checkContext, matcher, j,
		len = tokens.length,
		leadingRelative = Expr.relative[ tokens[ 0 ].type ],
		implicitRelative = leadingRelative || Expr.relative[ " " ],
		i = leadingRelative ? 1 : 0,

		// The foundational matcher ensures that elements are reachable from top-level context(s)
		matchContext = addCombinator( function( elem ) {
			return elem === checkContext;
		}, implicitRelative, true ),
		matchAnyContext = addCombinator( function( elem ) {
			return indexOf( checkContext, elem ) > -1;
		}, implicitRelative, true ),
		matchers = [ function( elem, context, xml ) {
			var ret = ( !leadingRelative && ( xml || context !== outermostContext ) ) || (
				( checkContext = context ).nodeType ?
					matchContext( elem, context, xml ) :
					matchAnyContext( elem, context, xml ) );

			// Avoid hanging onto element (issue #299)
			checkContext = null;
			return ret;
		} ];

	for ( ; i < len; i++ ) {
		if ( ( matcher = Expr.relative[ tokens[ i ].type ] ) ) {
			matchers = [ addCombinator( elementMatcher( matchers ), matcher ) ];
		} else {
			matcher = Expr.filter[ tokens[ i ].type ].apply( null, tokens[ i ].matches );

			// Return special upon seeing a positional matcher
			if ( matcher[ expando ] ) {

				// Find the next relative operator (if any) for proper handling
				j = ++i;
				for ( ; j < len; j++ ) {
					if ( Expr.relative[ tokens[ j ].type ] ) {
						break;
					}
				}
				return setMatcher(
					i > 1 && elementMatcher( matchers ),
					i > 1 && toSelector(

					// If the preceding token was a descendant combinator, insert an implicit any-element `*`
					tokens
						.slice( 0, i - 1 )
						.concat( { value: tokens[ i - 2 ].type === " " ? "*" : "" } )
					).replace( rtrim, "$1" ),
					matcher,
					i < j && matcherFromTokens( tokens.slice( i, j ) ),
					j < len && matcherFromTokens( ( tokens = tokens.slice( j ) ) ),
					j < len && toSelector( tokens )
				);
			}
			matchers.push( matcher );
		}
	}

	return elementMatcher( matchers );
}

function matcherFromGroupMatchers( elementMatchers, setMatchers ) {
	var bySet = setMatchers.length > 0,
		byElement = elementMatchers.length > 0,
		superMatcher = function( seed, context, xml, results, outermost ) {
			var elem, j, matcher,
				matchedCount = 0,
				i = "0",
				unmatched = seed && [],
				setMatched = [],
				contextBackup = outermostContext,

				// We must always have either seed elements or outermost context
				elems = seed || byElement && Expr.find[ "TAG" ]( "*", outermost ),

				// Use integer dirruns iff this is the outermost matcher
				dirrunsUnique = ( dirruns += contextBackup == null ? 1 : Math.random() || 0.1 ),
				len = elems.length;

			if ( outermost ) {

				// Support: IE 11+, Edge 17 - 18+
				// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
				// two documents; shallow comparisons work.
				// eslint-disable-next-line eqeqeq
				outermostContext = context == document || context || outermost;
			}

			// Add elements passing elementMatchers directly to results
			// Support: IE<9, Safari
			// Tolerate NodeList properties (IE: "length"; Safari: <number>) matching elements by id
			for ( ; i !== len && ( elem = elems[ i ] ) != null; i++ ) {
				if ( byElement && elem ) {
					j = 0;

					// Support: IE 11+, Edge 17 - 18+
					// IE/Edge sometimes throw a "Permission denied" error when strict-comparing
					// two documents; shallow comparisons work.
					// eslint-disable-next-line eqeqeq
					if ( !context && elem.ownerDocument != document ) {
						setDocument( elem );
						xml = !documentIsHTML;
					}
					while ( ( matcher = elementMatchers[ j++ ] ) ) {
						if ( matcher( elem, context || document, xml ) ) {
							results.push( elem );
							break;
						}
					}
					if ( outermost ) {
						dirruns = dirrunsUnique;
					}
				}

				// Track unmatched elements for set filters
				if ( bySet ) {

					// They will have gone through all possible matchers
					if ( ( elem = !matcher && elem ) ) {
						matchedCount--;
					}

					// Lengthen the array for every element, matched or not
					if ( seed ) {
						unmatched.push( elem );
					}
				}
			}

			// `i` is now the count of elements visited above, and adding it to `matchedCount`
			// makes the latter nonnegative.
			matchedCount += i;

			// Apply set filters to unmatched elements
			// NOTE: This can be skipped if there are no unmatched elements (i.e., `matchedCount`
			// equals `i`), unless we didn't visit _any_ elements in the above loop because we have
			// no element matchers and no seed.
			// Incrementing an initially-string "0" `i` allows `i` to remain a string only in that
			// case, which will result in a "00" `matchedCount` that differs from `i` but is also
			// numerically zero.
			if ( bySet && i !== matchedCount ) {
				j = 0;
				while ( ( matcher = setMatchers[ j++ ] ) ) {
					matcher( unmatched, setMatched, context, xml );
				}

				if ( seed ) {

					// Reintegrate element matches to eliminate the need for sorting
					if ( matchedCount > 0 ) {
						while ( i-- ) {
							if ( !( unmatched[ i ] || setMatched[ i ] ) ) {
								setMatched[ i ] = pop.call( results );
							}
						}
					}

					// Discard index placeholder values to get only actual matches
					setMatched = condense( setMatched );
				}

				// Add matches to results
				push.apply( results, setMatched );

				// Seedless set matches succeeding multiple successful matchers stipulate sorting
				if ( outermost && !seed && setMatched.length > 0 &&
					( matchedCount + setMatchers.length ) > 1 ) {

					Sizzle.uniqueSort( results );
				}
			}

			// Override manipulation of globals by nested matchers
			if ( outermost ) {
				dirruns = dirrunsUnique;
				outermostContext = contextBackup;
			}

			return unmatched;
		};

	return bySet ?
		markFunction( superMatcher ) :
		superMatcher;
}

compile = Sizzle.compile = function( selector, match /* Internal Use Only */ ) {
	var i,
		setMatchers = [],
		elementMatchers = [],
		cached = compilerCache[ selector + " " ];

	if ( !cached ) {

		// Generate a function of recursive functions that can be used to check each element
		if ( !match ) {
			match = tokenize( selector );
		}
		i = match.length;
		while ( i-- ) {
			cached = matcherFromTokens( match[ i ] );
			if ( cached[ expando ] ) {
				setMatchers.push( cached );
			} else {
				elementMatchers.push( cached );
			}
		}

		// Cache the compiled function
		cached = compilerCache(
			selector,
			matcherFromGroupMatchers( elementMatchers, setMatchers )
		);

		// Save selector and tokenization
		cached.selector = selector;
	}
	return cached;
};

/**
 * A low-level selection function that works with Sizzle's compiled
 *  selector functions
 * @param {String|Function} selector A selector or a pre-compiled
 *  selector function built with Sizzle.compile
 * @param {Element} context
 * @param {Array} [results]
 * @param {Array} [seed] A set of elements to match against
 */
select = Sizzle.select = function( selector, context, results, seed ) {
	var i, tokens, token, type, find,
		compiled = typeof selector === "function" && selector,
		match = !seed && tokenize( ( selector = compiled.selector || selector ) );

	results = results || [];

	// Try to minimize operations if there is only one selector in the list and no seed
	// (the latter of which guarantees us context)
	if ( match.length === 1 ) {

		// Reduce context if the leading compound selector is an ID
		tokens = match[ 0 ] = match[ 0 ].slice( 0 );
		if ( tokens.length > 2 && ( token = tokens[ 0 ] ).type === "ID" &&
			context.nodeType === 9 && documentIsHTML && Expr.relative[ tokens[ 1 ].type ] ) {

			context = ( Expr.find[ "ID" ]( token.matches[ 0 ]
				.replace( runescape, funescape ), context ) || [] )[ 0 ];
			if ( !context ) {
				return results;

			// Precompiled matchers will still verify ancestry, so step up a level
			} else if ( compiled ) {
				context = context.parentNode;
			}

			selector = selector.slice( tokens.shift().value.length );
		}

		// Fetch a seed set for right-to-left matching
		i = matchExpr[ "needsContext" ].test( selector ) ? 0 : tokens.length;
		while ( i-- ) {
			token = tokens[ i ];

			// Abort if we hit a combinator
			if ( Expr.relative[ ( type = token.type ) ] ) {
				break;
			}
			if ( ( find = Expr.find[ type ] ) ) {

				// Search, expanding context for leading sibling combinators
				if ( ( seed = find(
					token.matches[ 0 ].replace( runescape, funescape ),
					rsibling.test( tokens[ 0 ].type ) && testContext( context.parentNode ) ||
						context
				) ) ) {

					// If seed is empty or no tokens remain, we can return early
					tokens.splice( i, 1 );
					selector = seed.length && toSelector( tokens );
					if ( !selector ) {
						push.apply( results, seed );
						return results;
					}

					break;
				}
			}
		}
	}

	// Compile and execute a filtering function if one is not provided
	// Provide `match` to avoid retokenization if we modified the selector above
	( compiled || compile( selector, match ) )(
		seed,
		context,
		!documentIsHTML,
		results,
		!context || rsibling.test( selector ) && testContext( context.parentNode ) || context
	);
	return results;
};

// One-time assignments

// Sort stability
support.sortStable = expando.split( "" ).sort( sortOrder ).join( "" ) === expando;

// Support: Chrome 14-35+
// Always assume duplicates if they aren't passed to the comparison function
support.detectDuplicates = !!hasDuplicate;

// Initialize against the default document
setDocument();

// Support: Webkit<537.32 - Safari 6.0.3/Chrome 25 (fixed in Chrome 27)
// Detached nodes confoundingly follow *each other*
support.sortDetached = assert( function( el ) {

	// Should return 1, but returns 4 (following)
	return el.compareDocumentPosition( document.createElement( "fieldset" ) ) & 1;
} );

// Support: IE<8
// Prevent attribute/property "interpolation"
// https://msdn.microsoft.com/en-us/library/ms536429%28VS.85%29.aspx
if ( !assert( function( el ) {
	el.innerHTML = "<a href='#'></a>";
	return el.firstChild.getAttribute( "href" ) === "#";
} ) ) {
	addHandle( "type|href|height|width", function( elem, name, isXML ) {
		if ( !isXML ) {
			return elem.getAttribute( name, name.toLowerCase() === "type" ? 1 : 2 );
		}
	} );
}

// Support: IE<9
// Use defaultValue in place of getAttribute("value")
if ( !support.attributes || !assert( function( el ) {
	el.innerHTML = "<input/>";
	el.firstChild.setAttribute( "value", "" );
	return el.firstChild.getAttribute( "value" ) === "";
} ) ) {
	addHandle( "value", function( elem, _name, isXML ) {
		if ( !isXML && elem.nodeName.toLowerCase() === "input" ) {
			return elem.defaultValue;
		}
	} );
}

// Support: IE<9
// Use getAttributeNode to fetch booleans when getAttribute lies
if ( !assert( function( el ) {
	return el.getAttribute( "disabled" ) == null;
} ) ) {
	addHandle( booleans, function( elem, name, isXML ) {
		var val;
		if ( !isXML ) {
			return elem[ name ] === true ? name.toLowerCase() :
				( val = elem.getAttributeNode( name ) ) && val.specified ?
					val.value :
					null;
		}
	} );
}

return Sizzle;

} )( window );



jQuery.find = Sizzle;
jQuery.expr = Sizzle.selectors;

// Deprecated
jQuery.expr[ ":" ] = jQuery.expr.pseudos;
jQuery.uniqueSort = jQuery.unique = Sizzle.uniqueSort;
jQuery.text = Sizzle.getText;
jQuery.isXMLDoc = Sizzle.isXML;
jQuery.contains = Sizzle.contains;
jQuery.escapeSelector = Sizzle.escape;




var dir = function( elem, dir, until ) {
	var matched = [],
		truncate = until !== undefined;

	while ( ( elem = elem[ dir ] ) && elem.nodeType !== 9 ) {
		if ( elem.nodeType === 1 ) {
			if ( truncate && jQuery( elem ).is( until ) ) {
				break;
			}
			matched.push( elem );
		}
	}
	return matched;
};


var siblings = function( n, elem ) {
	var matched = [];

	for ( ; n; n = n.nextSibling ) {
		if ( n.nodeType === 1 && n !== elem ) {
			matched.push( n );
		}
	}

	return matched;
};


var rneedsContext = jQuery.expr.match.needsContext;



function nodeName( elem, name ) {

  return elem.nodeName && elem.nodeName.toLowerCase() === name.toLowerCase();

};
var rsingleTag = ( /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i );



// Implement the identical functionality for filter and not
function winnow( elements, qualifier, not ) {
	if ( isFunction( qualifier ) ) {
		return jQuery.grep( elements, function( elem, i ) {
			return !!qualifier.call( elem, i, elem ) !== not;
		} );
	}

	// Single element
	if ( qualifier.nodeType ) {
		return jQuery.grep( elements, function( elem ) {
			return ( elem === qualifier ) !== not;
		} );
	}

	// Arraylike of elements (jQuery, arguments, Array)
	if ( typeof qualifier !== "string" ) {
		return jQuery.grep( elements, function( elem ) {
			return ( indexOf.call( qualifier, elem ) > -1 ) !== not;
		} );
	}

	// Filtered directly for both simple and complex selectors
	return jQuery.filter( qualifier, elements, not );
}

jQuery.filter = function( expr, elems, not ) {
	var elem = elems[ 0 ];

	if ( not ) {
		expr = ":not(" + expr + ")";
	}

	if ( elems.length === 1 && elem.nodeType === 1 ) {
		return jQuery.find.matchesSelector( elem, expr ) ? [ elem ] : [];
	}

	return jQuery.find.matches( expr, jQuery.grep( elems, function( elem ) {
		return elem.nodeType === 1;
	} ) );
};

jQuery.fn.extend( {
	find: function( selector ) {
		var i, ret,
			len = this.length,
			self = this;

		if ( typeof selector !== "string" ) {
			return this.pushStack( jQuery( selector ).filter( function() {
				for ( i = 0; i < len; i++ ) {
					if ( jQuery.contains( self[ i ], this ) ) {
						return true;
					}
				}
			} ) );
		}

		ret = this.pushStack( [] );

		for ( i = 0; i < len; i++ ) {
			jQuery.find( selector, self[ i ], ret );
		}

		return len > 1 ? jQuery.uniqueSort( ret ) : ret;
	},
	filter: function( selector ) {
		return this.pushStack( winnow( this, selector || [], false ) );
	},
	not: function( selector ) {
		return this.pushStack( winnow( this, selector || [], true ) );
	},
	is: function( selector ) {
		return !!winnow(
			this,

			// If this is a positional/relative selector, check membership in the returned set
			// so $("p:first").is("p:last") won't return true for a doc with two "p".
			typeof selector === "string" && rneedsContext.test( selector ) ?
				jQuery( selector ) :
				selector || [],
			false
		).length;
	}
} );


// Initialize a jQuery object


// A central reference to the root jQuery(document)
var rootjQuery,

	// A simple way to check for HTML strings
	// Prioritize #id over <tag> to avoid XSS via location.hash (#9521)
	// Strict HTML recognition (#11290: must start with <)
	// Shortcut simple #id case for speed
	rquickExpr = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/,

	init = jQuery.fn.init = function( selector, context, root ) {
		var match, elem;

		// HANDLE: $(""), $(null), $(undefined), $(false)
		if ( !selector ) {
			return this;
		}

		// Method init() accepts an alternate rootjQuery
		// so migrate can support jQuery.sub (gh-2101)
		root = root || rootjQuery;

		// Handle HTML strings
		if ( typeof selector === "string" ) {
			if ( selector[ 0 ] === "<" &&
				selector[ selector.length - 1 ] === ">" &&
				selector.length >= 3 ) {

				// Assume that strings that start and end with <> are HTML and skip the regex check
				match = [ null, selector, null ];

			} else {
				match = rquickExpr.exec( selector );
			}

			// Match html or make sure no context is specified for #id
			if ( match && ( match[ 1 ] || !context ) ) {

				// HANDLE: $(html) -> $(array)
				if ( match[ 1 ] ) {
					context = context instanceof jQuery ? context[ 0 ] : context;

					// Option to run scripts is true for back-compat
					// Intentionally let the error be thrown if parseHTML is not present
					jQuery.merge( this, jQuery.parseHTML(
						match[ 1 ],
						context && context.nodeType ? context.ownerDocument || context : document,
						true
					) );

					// HANDLE: $(html, props)
					if ( rsingleTag.test( match[ 1 ] ) && jQuery.isPlainObject( context ) ) {
						for ( match in context ) {

							// Properties of context are called as methods if possible
							if ( isFunction( this[ match ] ) ) {
								this[ match ]( context[ match ] );

							// ...and otherwise set as attributes
							} else {
								this.attr( match, context[ match ] );
							}
						}
					}

					return this;

				// HANDLE: $(#id)
				} else {
					elem = document.getElementById( match[ 2 ] );

					if ( elem ) {

						// Inject the element directly into the jQuery object
						this[ 0 ] = elem;
						this.length = 1;
					}
					return this;
				}

			// HANDLE: $(expr, $(...))
			} else if ( !context || context.jquery ) {
				return ( context || root ).find( selector );

			// HANDLE: $(expr, context)
			// (which is just equivalent to: $(context).find(expr)
			} else {
				return this.constructor( context ).find( selector );
			}

		// HANDLE: $(DOMElement)
		} else if ( selector.nodeType ) {
			this[ 0 ] = selector;
			this.length = 1;
			return this;

		// HANDLE: $(function)
		// Shortcut for document ready
		} else if ( isFunction( selector ) ) {
			return root.ready !== undefined ?
				root.ready( selector ) :

				// Execute immediately if ready is not present
				selector( jQuery );
		}

		return jQuery.makeArray( selector, this );
	};

// Give the init function the jQuery prototype for later instantiation
init.prototype = jQuery.fn;

// Initialize central reference
rootjQuery = jQuery( document );


var rparentsprev = /^(?:parents|prev(?:Until|All))/,

	// Methods guaranteed to produce a unique set when starting from a unique set
	guaranteedUnique = {
		children: true,
		contents: true,
		next: true,
		prev: true
	};

jQuery.fn.extend( {
	has: function( target ) {
		var targets = jQuery( target, this ),
			l = targets.length;

		return this.filter( function() {
			var i = 0;
			for ( ; i < l; i++ ) {
				if ( jQuery.contains( this, targets[ i ] ) ) {
					return true;
				}
			}
		} );
	},

	closest: function( selectors, context ) {
		var cur,
			i = 0,
			l = this.length,
			matched = [],
			targets = typeof selectors !== "string" && jQuery( selectors );

		// Positional selectors never match, since there's no _selection_ context
		if ( !rneedsContext.test( selectors ) ) {
			for ( ; i < l; i++ ) {
				for ( cur = this[ i ]; cur && cur !== context; cur = cur.parentNode ) {

					// Always skip document fragments
					if ( cur.nodeType < 11 && ( targets ?
						targets.index( cur ) > -1 :

						// Don't pass non-elements to Sizzle
						cur.nodeType === 1 &&
							jQuery.find.matchesSelector( cur, selectors ) ) ) {

						matched.push( cur );
						break;
					}
				}
			}
		}

		return this.pushStack( matched.length > 1 ? jQuery.uniqueSort( matched ) : matched );
	},

	// Determine the position of an element within the set
	index: function( elem ) {

		// No argument, return index in parent
		if ( !elem ) {
			return ( this[ 0 ] && this[ 0 ].parentNode ) ? this.first().prevAll().length : -1;
		}

		// Index in selector
		if ( typeof elem === "string" ) {
			return indexOf.call( jQuery( elem ), this[ 0 ] );
		}

		// Locate the position of the desired element
		return indexOf.call( this,

			// If it receives a jQuery object, the first element is used
			elem.jquery ? elem[ 0 ] : elem
		);
	},

	add: function( selector, context ) {
		return this.pushStack(
			jQuery.uniqueSort(
				jQuery.merge( this.get(), jQuery( selector, context ) )
			)
		);
	},

	addBack: function( selector ) {
		return this.add( selector == null ?
			this.prevObject : this.prevObject.filter( selector )
		);
	}
} );

function sibling( cur, dir ) {
	while ( ( cur = cur[ dir ] ) && cur.nodeType !== 1 ) {}
	return cur;
}

jQuery.each( {
	parent: function( elem ) {
		var parent = elem.parentNode;
		return parent && parent.nodeType !== 11 ? parent : null;
	},
	parents: function( elem ) {
		return dir( elem, "parentNode" );
	},
	parentsUntil: function( elem, _i, until ) {
		return dir( elem, "parentNode", until );
	},
	next: function( elem ) {
		return sibling( elem, "nextSibling" );
	},
	prev: function( elem ) {
		return sibling( elem, "previousSibling" );
	},
	nextAll: function( elem ) {
		return dir( elem, "nextSibling" );
	},
	prevAll: function( elem ) {
		return dir( elem, "previousSibling" );
	},
	nextUntil: function( elem, _i, until ) {
		return dir( elem, "nextSibling", until );
	},
	prevUntil: function( elem, _i, until ) {
		return dir( elem, "previousSibling", until );
	},
	siblings: function( elem ) {
		return siblings( ( elem.parentNode || {} ).firstChild, elem );
	},
	children: function( elem ) {
		return siblings( elem.firstChild );
	},
	contents: function( elem ) {
		if ( elem.contentDocument != null &&

			// Support: IE 11+
			// <object> elements with no `data` attribute has an object
			// `contentDocument` with a `null` prototype.
			getProto( elem.contentDocument ) ) {

			return elem.contentDocument;
		}

		// Support: IE 9 - 11 only, iOS 7 only, Android Browser <=4.3 only
		// Treat the template element as a regular one in browsers that
		// don't support it.
		if ( nodeName( elem, "template" ) ) {
			elem = elem.content || elem;
		}

		return jQuery.merge( [], elem.childNodes );
	}
}, function( name, fn ) {
	jQuery.fn[ name ] = function( until, selector ) {
		var matched = jQuery.map( this, fn, until );

		if ( name.slice( -5 ) !== "Until" ) {
			selector = until;
		}

		if ( selector && typeof selector === "string" ) {
			matched = jQuery.filter( selector, matched );
		}

		if ( this.length > 1 ) {

			// Remove duplicates
			if ( !guaranteedUnique[ name ] ) {
				jQuery.uniqueSort( matched );
			}

			// Reverse order for parents* and prev-derivatives
			if ( rparentsprev.test( name ) ) {
				matched.reverse();
			}
		}

		return this.pushStack( matched );
	};
} );
var rnothtmlwhite = ( /[^\x20\t\r\n\f]+/g );



// Convert String-formatted options into Object-formatted ones
function createOptions( options ) {
	var object = {};
	jQuery.each( options.match( rnothtmlwhite ) || [], function( _, flag ) {
		object[ flag ] = true;
	} );
	return object;
}

/*
 * Create a callback list using the following parameters:
 *
 *	options: an optional list of space-separated options that will change how
 *			the callback list behaves or a more traditional option object
 *
 * By default a callback list will act like an event callback list and can be
 * "fired" multiple times.
 *
 * Possible options:
 *
 *	once:			will ensure the callback list can only be fired once (like a Deferred)
 *
 *	memory:			will keep track of previous values and will call any callback added
 *					after the list has been fired right away with the latest "memorized"
 *					values (like a Deferred)
 *
 *	unique:			will ensure a callback can only be added once (no duplicate in the list)
 *
 *	stopOnFalse:	interrupt callings when a callback returns false
 *
 */
jQuery.Callbacks = function( options ) {

	// Convert options from String-formatted to Object-formatted if needed
	// (we check in cache first)
	options = typeof options === "string" ?
		createOptions( options ) :
		jQuery.extend( {}, options );

	var // Flag to know if list is currently firing
		firing,

		// Last fire value for non-forgettable lists
		memory,

		// Flag to know if list was already fired
		fired,

		// Flag to prevent firing
		locked,

		// Actual callback list
		list = [],

		// Queue of execution data for repeatable lists
		queue = [],

		// Index of currently firing callback (modified by add/remove as needed)
		firingIndex = -1,

		// Fire callbacks
		fire = function() {

			// Enforce single-firing
			locked = locked || options.once;

			// Execute callbacks for all pending executions,
			// respecting firingIndex overrides and runtime changes
			fired = firing = true;
			for ( ; queue.length; firingIndex = -1 ) {
				memory = queue.shift();
				while ( ++firingIndex < list.length ) {

					// Run callback and check for early termination
					if ( list[ firingIndex ].apply( memory[ 0 ], memory[ 1 ] ) === false &&
						options.stopOnFalse ) {

						// Jump to end and forget the data so .add doesn't re-fire
						firingIndex = list.length;
						memory = false;
					}
				}
			}

			// Forget the data if we're done with it
			if ( !options.memory ) {
				memory = false;
			}

			firing = false;

			// Clean up if we're done firing for good
			if ( locked ) {

				// Keep an empty list if we have data for future add calls
				if ( memory ) {
					list = [];

				// Otherwise, this object is spent
				} else {
					list = "";
				}
			}
		},

		// Actual Callbacks object
		self = {

			// Add a callback or a collection of callbacks to the list
			add: function() {
				if ( list ) {

					// If we have memory from a past run, we should fire after adding
					if ( memory && !firing ) {
						firingIndex = list.length - 1;
						queue.push( memory );
					}

					( function add( args ) {
						jQuery.each( args, function( _, arg ) {
							if ( isFunction( arg ) ) {
								if ( !options.unique || !self.has( arg ) ) {
									list.push( arg );
								}
							} else if ( arg && arg.length && toType( arg ) !== "string" ) {

								// Inspect recursively
								add( arg );
							}
						} );
					} )( arguments );

					if ( memory && !firing ) {
						fire();
					}
				}
				return this;
			},

			// Remove a callback from the list
			remove: function() {
				jQuery.each( arguments, function( _, arg ) {
					var index;
					while ( ( index = jQuery.inArray( arg, list, index ) ) > -1 ) {
						list.splice( index, 1 );

						// Handle firing indexes
						if ( index <= firingIndex ) {
							firingIndex--;
						}
					}
				} );
				return this;
			},

			// Check if a given callback is in the list.
			// If no argument is given, return whether or not list has callbacks attached.
			has: function( fn ) {
				return fn ?
					jQuery.inArray( fn, list ) > -1 :
					list.length > 0;
			},

			// Remove all callbacks from the list
			empty: function() {
				if ( list ) {
					list = [];
				}
				return this;
			},

			// Disable .fire and .add
			// Abort any current/pending executions
			// Clear all callbacks and values
			disable: function() {
				locked = queue = [];
				list = memory = "";
				return this;
			},
			disabled: function() {
				return !list;
			},

			// Disable .fire
			// Also disable .add unless we have memory (since it would have no effect)
			// Abort any pending executions
			lock: function() {
				locked = queue = [];
				if ( !memory && !firing ) {
					list = memory = "";
				}
				return this;
			},
			locked: function() {
				return !!locked;
			},

			// Call all callbacks with the given context and arguments
			fireWith: function( context, args ) {
				if ( !locked ) {
					args = args || [];
					args = [ context, args.slice ? args.slice() : args ];
					queue.push( args );
					if ( !firing ) {
						fire();
					}
				}
				return this;
			},

			// Call all the callbacks with the given arguments
			fire: function() {
				self.fireWith( this, arguments );
				return this;
			},

			// To know if the callbacks have already been called at least once
			fired: function() {
				return !!fired;
			}
		};

	return self;
};


function Identity( v ) {
	return v;
}
function Thrower( ex ) {
	throw ex;
}

function adoptValue( value, resolve, reject, noValue ) {
	var method;

	try {

		// Check for promise aspect first to privilege synchronous behavior
		if ( value && isFunction( ( method = value.promise ) ) ) {
			method.call( value ).done( resolve ).fail( reject );

		// Other thenables
		} else if ( value && isFunction( ( method = value.then ) ) ) {
			method.call( value, resolve, reject );

		// Other non-thenables
		} else {

			// Control `resolve` arguments by letting Array#slice cast boolean `noValue` to integer:
			// * false: [ value ].slice( 0 ) => resolve( value )
			// * true: [ value ].slice( 1 ) => resolve()
			resolve.apply( undefined, [ value ].slice( noValue ) );
		}

	// For Promises/A+, convert exceptions into rejections
	// Since jQuery.when doesn't unwrap thenables, we can skip the extra checks appearing in
	// Deferred#then to conditionally suppress rejection.
	} catch ( value ) {

		// Support: Android 4.0 only
		// Strict mode functions invoked without .call/.apply get global-object context
		reject.apply( undefined, [ value ] );
	}
}

jQuery.extend( {

	Deferred: function( func ) {
		var tuples = [

				// action, add listener, callbacks,
				// ... .then handlers, argument index, [final state]
				[ "notify", "progress", jQuery.Callbacks( "memory" ),
					jQuery.Callbacks( "memory" ), 2 ],
				[ "resolve", "done", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 0, "resolved" ],
				[ "reject", "fail", jQuery.Callbacks( "once memory" ),
					jQuery.Callbacks( "once memory" ), 1, "rejected" ]
			],
			state = "pending",
			promise = {
				state: function() {
					return state;
				},
				always: function() {
					deferred.done( arguments ).fail( arguments );
					return this;
				},
				"catch": function( fn ) {
					return promise.then( null, fn );
				},

				// Keep pipe for back-compat
				pipe: function( /* fnDone, fnFail, fnProgress */ ) {
					var fns = arguments;

					return jQuery.Deferred( function( newDefer ) {
						jQuery.each( tuples, function( _i, tuple ) {

							// Map tuples (progress, done, fail) to arguments (done, fail, progress)
							var fn = isFunction( fns[ tuple[ 4 ] ] ) && fns[ tuple[ 4 ] ];

							// deferred.progress(function() { bind to newDefer or newDefer.notify })
							// deferred.done(function() { bind to newDefer or newDefer.resolve })
							// deferred.fail(function() { bind to newDefer or newDefer.reject })
							deferred[ tuple[ 1 ] ]( function() {
								var returned = fn && fn.apply( this, arguments );
								if ( returned && isFunction( returned.promise ) ) {
									returned.promise()
										.progress( newDefer.notify )
										.done( newDefer.resolve )
										.fail( newDefer.reject );
								} else {
									newDefer[ tuple[ 0 ] + "With" ](
										this,
										fn ? [ returned ] : arguments
									);
								}
							} );
						} );
						fns = null;
					} ).promise();
				},
				then: function( onFulfilled, onRejected, onProgress ) {
					var maxDepth = 0;
					function resolve( depth, deferred, handler, special ) {
						return function() {
							var that = this,
								args = arguments,
								mightThrow = function() {
									var returned, then;

									// Support: Promises/A+ section 2.3.3.3.3
									// https://promisesaplus.com/#point-59
									// Ignore double-resolution attempts
									if ( depth < maxDepth ) {
										return;
									}

									returned = handler.apply( that, args );

									// Support: Promises/A+ section 2.3.1
									// https://promisesaplus.com/#point-48
									if ( returned === deferred.promise() ) {
										throw new TypeError( "Thenable self-resolution" );
									}

									// Support: Promises/A+ sections 2.3.3.1, 3.5
									// https://promisesaplus.com/#point-54
									// https://promisesaplus.com/#point-75
									// Retrieve `then` only once
									then = returned &&

										// Support: Promises/A+ section 2.3.4
										// https://promisesaplus.com/#point-64
										// Only check objects and functions for thenability
										( typeof returned === "object" ||
											typeof returned === "function" ) &&
										returned.then;

									// Handle a returned thenable
									if ( isFunction( then ) ) {

										// Special processors (notify) just wait for resolution
										if ( special ) {
											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special )
											);

										// Normal processors (resolve) also hook into progress
										} else {

											// ...and disregard older resolution values
											maxDepth++;

											then.call(
												returned,
												resolve( maxDepth, deferred, Identity, special ),
												resolve( maxDepth, deferred, Thrower, special ),
												resolve( maxDepth, deferred, Identity,
													deferred.notifyWith )
											);
										}

									// Handle all other returned values
									} else {

										// Only substitute handlers pass on context
										// and multiple values (non-spec behavior)
										if ( handler !== Identity ) {
											that = undefined;
											args = [ returned ];
										}

										// Process the value(s)
										// Default process is resolve
										( special || deferred.resolveWith )( that, args );
									}
								},

								// Only normal processors (resolve) catch and reject exceptions
								process = special ?
									mightThrow :
									function() {
										try {
											mightThrow();
										} catch ( e ) {

											if ( jQuery.Deferred.exceptionHook ) {
												jQuery.Deferred.exceptionHook( e,
													process.stackTrace );
											}

											// Support: Promises/A+ section 2.3.3.3.4.1
											// https://promisesaplus.com/#point-61
											// Ignore post-resolution exceptions
											if ( depth + 1 >= maxDepth ) {

												// Only substitute handlers pass on context
												// and multiple values (non-spec behavior)
												if ( handler !== Thrower ) {
													that = undefined;
													args = [ e ];
												}

												deferred.rejectWith( that, args );
											}
										}
									};

							// Support: Promises/A+ section 2.3.3.3.1
							// https://promisesaplus.com/#point-57
							// Re-resolve promises immediately to dodge false rejection from
							// subsequent errors
							if ( depth ) {
								process();
							} else {

								// Call an optional hook to record the stack, in case of exception
								// since it's otherwise lost when execution goes async
								if ( jQuery.Deferred.getStackHook ) {
									process.stackTrace = jQuery.Deferred.getStackHook();
								}
								window.setTimeout( process );
							}
						};
					}

					return jQuery.Deferred( function( newDefer ) {

						// progress_handlers.add( ... )
						tuples[ 0 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onProgress ) ?
									onProgress :
									Identity,
								newDefer.notifyWith
							)
						);

						// fulfilled_handlers.add( ... )
						tuples[ 1 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onFulfilled ) ?
									onFulfilled :
									Identity
							)
						);

						// rejected_handlers.add( ... )
						tuples[ 2 ][ 3 ].add(
							resolve(
								0,
								newDefer,
								isFunction( onRejected ) ?
									onRejected :
									Thrower
							)
						);
					} ).promise();
				},

				// Get a promise for this deferred
				// If obj is provided, the promise aspect is added to the object
				promise: function( obj ) {
					return obj != null ? jQuery.extend( obj, promise ) : promise;
				}
			},
			deferred = {};

		// Add list-specific methods
		jQuery.each( tuples, function( i, tuple ) {
			var list = tuple[ 2 ],
				stateString = tuple[ 5 ];

			// promise.progress = list.add
			// promise.done = list.add
			// promise.fail = list.add
			promise[ tuple[ 1 ] ] = list.add;

			// Handle state
			if ( stateString ) {
				list.add(
					function() {

						// state = "resolved" (i.e., fulfilled)
						// state = "rejected"
						state = stateString;
					},

					// rejected_callbacks.disable
					// fulfilled_callbacks.disable
					tuples[ 3 - i ][ 2 ].disable,

					// rejected_handlers.disable
					// fulfilled_handlers.disable
					tuples[ 3 - i ][ 3 ].disable,

					// progress_callbacks.lock
					tuples[ 0 ][ 2 ].lock,

					// progress_handlers.lock
					tuples[ 0 ][ 3 ].lock
				);
			}

			// progress_handlers.fire
			// fulfilled_handlers.fire
			// rejected_handlers.fire
			list.add( tuple[ 3 ].fire );

			// deferred.notify = function() { deferred.notifyWith(...) }
			// deferred.resolve = function() { deferred.resolveWith(...) }
			// deferred.reject = function() { deferred.rejectWith(...) }
			deferred[ tuple[ 0 ] ] = function() {
				deferred[ tuple[ 0 ] + "With" ]( this === deferred ? undefined : this, arguments );
				return this;
			};

			// deferred.notifyWith = list.fireWith
			// deferred.resolveWith = list.fireWith
			// deferred.rejectWith = list.fireWith
			deferred[ tuple[ 0 ] + "With" ] = list.fireWith;
		} );

		// Make the deferred a promise
		promise.promise( deferred );

		// Call given func if any
		if ( func ) {
			func.call( deferred, deferred );
		}

		// All done!
		return deferred;
	},

	// Deferred helper
	when: function( singleValue ) {
		var

			// count of uncompleted subordinates
			remaining = arguments.length,

			// count of unprocessed arguments
			i = remaining,

			// subordinate fulfillment data
			resolveContexts = Array( i ),
			resolveValues = slice.call( arguments ),

			// the master Deferred
			master = jQuery.Deferred(),

			// subordinate callback factory
			updateFunc = function( i ) {
				return function( value ) {
					resolveContexts[ i ] = this;
					resolveValues[ i ] = arguments.length > 1 ? slice.call( arguments ) : value;
					if ( !( --remaining ) ) {
						master.resolveWith( resolveContexts, resolveValues );
					}
				};
			};

		// Single- and empty arguments are adopted like Promise.resolve
		if ( remaining <= 1 ) {
			adoptValue( singleValue, master.done( updateFunc( i ) ).resolve, master.reject,
				!remaining );

			// Use .then() to unwrap secondary thenables (cf. gh-3000)
			if ( master.state() === "pending" ||
				isFunction( resolveValues[ i ] && resolveValues[ i ].then ) ) {

				return master.then();
			}
		}

		// Multiple arguments are aggregated like Promise.all array elements
		while ( i-- ) {
			adoptValue( resolveValues[ i ], updateFunc( i ), master.reject );
		}

		return master.promise();
	}
} );


// These usually indicate a programmer mistake during development,
// warn about them ASAP rather than swallowing them by default.
var rerrorNames = /^(Eval|Internal|Range|Reference|Syntax|Type|URI)Error$/;

jQuery.Deferred.exceptionHook = function( error, stack ) {

	// Support: IE 8 - 9 only
	// Console exists when dev tools are open, which can happen at any time
	if ( window.console && window.console.warn && error && rerrorNames.test( error.name ) ) {
		window.console.warn( "jQuery.Deferred exception: " + error.message, error.stack, stack );
	}
};




jQuery.readyException = function( error ) {
	window.setTimeout( function() {
		throw error;
	} );
};




// The deferred used on DOM ready
var readyList = jQuery.Deferred();

jQuery.fn.ready = function( fn ) {

	readyList
		.then( fn )

		// Wrap jQuery.readyException in a function so that the lookup
		// happens at the time of error handling instead of callback
		// registration.
		.catch( function( error ) {
			jQuery.readyException( error );
		} );

	return this;
};

jQuery.extend( {

	// Is the DOM ready to be used? Set to true once it occurs.
	isReady: false,

	// A counter to track how many items to wait for before
	// the ready event fires. See #6781
	readyWait: 1,

	// Handle when the DOM is ready
	ready: function( wait ) {

		// Abort if there are pending holds or we're already ready
		if ( wait === true ? --jQuery.readyWait : jQuery.isReady ) {
			return;
		}

		// Remember that the DOM is ready
		jQuery.isReady = true;

		// If a normal DOM Ready event fired, decrement, and wait if need be
		if ( wait !== true && --jQuery.readyWait > 0 ) {
			return;
		}

		// If there are functions bound, to execute
		readyList.resolveWith( document, [ jQuery ] );
	}
} );

jQuery.ready.then = readyList.then;

// The ready event handler and self cleanup method
function completed() {
	document.removeEventListener( "DOMContentLoaded", completed );
	window.removeEventListener( "load", completed );
	jQuery.ready();
}

// Catch cases where $(document).ready() is called
// after the browser event has already occurred.
// Support: IE <=9 - 10 only
// Older IE sometimes signals "interactive" too soon
if ( document.readyState === "complete" ||
	( document.readyState !== "loading" && !document.documentElement.doScroll ) ) {

	// Handle it asynchronously to allow scripts the opportunity to delay ready
	window.setTimeout( jQuery.ready );

} else {

	// Use the handy event callback
	document.addEventListener( "DOMContentLoaded", completed );

	// A fallback to window.onload, that will always work
	window.addEventListener( "load", completed );
}




// Multifunctional method to get and set values of a collection
// The value/s can optionally be executed if it's a function
var access = function( elems, fn, key, value, chainable, emptyGet, raw ) {
	var i = 0,
		len = elems.length,
		bulk = key == null;

	// Sets many values
	if ( toType( key ) === "object" ) {
		chainable = true;
		for ( i in key ) {
			access( elems, fn, i, key[ i ], true, emptyGet, raw );
		}

	// Sets one value
	} else if ( value !== undefined ) {
		chainable = true;

		if ( !isFunction( value ) ) {
			raw = true;
		}

		if ( bulk ) {

			// Bulk operations run against the entire set
			if ( raw ) {
				fn.call( elems, value );
				fn = null;

			// ...except when executing function values
			} else {
				bulk = fn;
				fn = function( elem, _key, value ) {
					return bulk.call( jQuery( elem ), value );
				};
			}
		}

		if ( fn ) {
			for ( ; i < len; i++ ) {
				fn(
					elems[ i ], key, raw ?
					value :
					value.call( elems[ i ], i, fn( elems[ i ], key ) )
				);
			}
		}
	}

	if ( chainable ) {
		return elems;
	}

	// Gets
	if ( bulk ) {
		return fn.call( elems );
	}

	return len ? fn( elems[ 0 ], key ) : emptyGet;
};


// Matches dashed string for camelizing
var rmsPrefix = /^-ms-/,
	rdashAlpha = /-([a-z])/g;

// Used by camelCase as callback to replace()
function fcamelCase( _all, letter ) {
	return letter.toUpperCase();
}

// Convert dashed to camelCase; used by the css and data modules
// Support: IE <=9 - 11, Edge 12 - 15
// Microsoft forgot to hump their vendor prefix (#9572)
function camelCase( string ) {
	return string.replace( rmsPrefix, "ms-" ).replace( rdashAlpha, fcamelCase );
}
var acceptData = function( owner ) {

	// Accepts only:
	//  - Node
	//    - Node.ELEMENT_NODE
	//    - Node.DOCUMENT_NODE
	//  - Object
	//    - Any
	return owner.nodeType === 1 || owner.nodeType === 9 || !( +owner.nodeType );
};




function Data() {
	this.expando = jQuery.expando + Data.uid++;
}

Data.uid = 1;

Data.prototype = {

	cache: function( owner ) {

		// Check if the owner object already has a cache
		var value = owner[ this.expando ];

		// If not, create one
		if ( !value ) {
			value = {};

			// We can accept data for non-element nodes in modern browsers,
			// but we should not, see #8335.
			// Always return an empty object.
			if ( acceptData( owner ) ) {

				// If it is a node unlikely to be stringify-ed or looped over
				// use plain assignment
				if ( owner.nodeType ) {
					owner[ this.expando ] = value;

				// Otherwise secure it in a non-enumerable property
				// configurable must be true to allow the property to be
				// deleted when data is removed
				} else {
					Object.defineProperty( owner, this.expando, {
						value: value,
						configurable: true
					} );
				}
			}
		}

		return value;
	},
	set: function( owner, data, value ) {
		var prop,
			cache = this.cache( owner );

		// Handle: [ owner, key, value ] args
		// Always use camelCase key (gh-2257)
		if ( typeof data === "string" ) {
			cache[ camelCase( data ) ] = value;

		// Handle: [ owner, { properties } ] args
		} else {

			// Copy the properties one-by-one to the cache object
			for ( prop in data ) {
				cache[ camelCase( prop ) ] = data[ prop ];
			}
		}
		return cache;
	},
	get: function( owner, key ) {
		return key === undefined ?
			this.cache( owner ) :

			// Always use camelCase key (gh-2257)
			owner[ this.expando ] && owner[ this.expando ][ camelCase( key ) ];
	},
	access: function( owner, key, value ) {

		// In cases where either:
		//
		//   1. No key was specified
		//   2. A string key was specified, but no value provided
		//
		// Take the "read" path and allow the get method to determine
		// which value to return, respectively either:
		//
		//   1. The entire cache object
		//   2. The data stored at the key
		//
		if ( key === undefined ||
				( ( key && typeof key === "string" ) && value === undefined ) ) {

			return this.get( owner, key );
		}

		// When the key is not a string, or both a key and value
		// are specified, set or extend (existing objects) with either:
		//
		//   1. An object of properties
		//   2. A key and value
		//
		this.set( owner, key, value );

		// Since the "set" path can have two possible entry points
		// return the expected data based on which path was taken[*]
		return value !== undefined ? value : key;
	},
	remove: function( owner, key ) {
		var i,
			cache = owner[ this.expando ];

		if ( cache === undefined ) {
			return;
		}

		if ( key !== undefined ) {

			// Support array or space separated string of keys
			if ( Array.isArray( key ) ) {

				// If key is an array of keys...
				// We always set camelCase keys, so remove that.
				key = key.map( camelCase );
			} else {
				key = camelCase( key );

				// If a key with the spaces exists, use it.
				// Otherwise, create an array by matching non-whitespace
				key = key in cache ?
					[ key ] :
					( key.match( rnothtmlwhite ) || [] );
			}

			i = key.length;

			while ( i-- ) {
				delete cache[ key[ i ] ];
			}
		}

		// Remove the expando if there's no more data
		if ( key === undefined || jQuery.isEmptyObject( cache ) ) {

			// Support: Chrome <=35 - 45
			// Webkit & Blink performance suffers when deleting properties
			// from DOM nodes, so set to undefined instead
			// https://bugs.chromium.org/p/chromium/issues/detail?id=378607 (bug restricted)
			if ( owner.nodeType ) {
				owner[ this.expando ] = undefined;
			} else {
				delete owner[ this.expando ];
			}
		}
	},
	hasData: function( owner ) {
		var cache = owner[ this.expando ];
		return cache !== undefined && !jQuery.isEmptyObject( cache );
	}
};
var dataPriv = new Data();

var dataUser = new Data();



//	Implementation Summary
//
//	1. Enforce API surface and semantic compatibility with 1.9.x branch
//	2. Improve the module's maintainability by reducing the storage
//		paths to a single mechanism.
//	3. Use the same single mechanism to support "private" and "user" data.
//	4. _Never_ expose "private" data to user code (TODO: Drop _data, _removeData)
//	5. Avoid exposing implementation details on user objects (eg. expando properties)
//	6. Provide a clear path for implementation upgrade to WeakMap in 2014

var rbrace = /^(?:\{[\w\W]*\}|\[[\w\W]*\])$/,
	rmultiDash = /[A-Z]/g;

function getData( data ) {
	if ( data === "true" ) {
		return true;
	}

	if ( data === "false" ) {
		return false;
	}

	if ( data === "null" ) {
		return null;
	}

	// Only convert to a number if it doesn't change the string
	if ( data === +data + "" ) {
		return +data;
	}

	if ( rbrace.test( data ) ) {
		return JSON.parse( data );
	}

	return data;
}

function dataAttr( elem, key, data ) {
	var name;

	// If nothing was found internally, try to fetch any
	// data from the HTML5 data-* attribute
	if ( data === undefined && elem.nodeType === 1 ) {
		name = "data-" + key.replace( rmultiDash, "-$&" ).toLowerCase();
		data = elem.getAttribute( name );

		if ( typeof data === "string" ) {
			try {
				data = getData( data );
			} catch ( e ) {}

			// Make sure we set the data so it isn't changed later
			dataUser.set( elem, key, data );
		} else {
			data = undefined;
		}
	}
	return data;
}

jQuery.extend( {
	hasData: function( elem ) {
		return dataUser.hasData( elem ) || dataPriv.hasData( elem );
	},

	data: function( elem, name, data ) {
		return dataUser.access( elem, name, data );
	},

	removeData: function( elem, name ) {
		dataUser.remove( elem, name );
	},

	// TODO: Now that all calls to _data and _removeData have been replaced
	// with direct calls to dataPriv methods, these can be deprecated.
	_data: function( elem, name, data ) {
		return dataPriv.access( elem, name, data );
	},

	_removeData: function( elem, name ) {
		dataPriv.remove( elem, name );
	}
} );

jQuery.fn.extend( {
	data: function( key, value ) {
		var i, name, data,
			elem = this[ 0 ],
			attrs = elem && elem.attributes;

		// Gets all values
		if ( key === undefined ) {
			if ( this.length ) {
				data = dataUser.get( elem );

				if ( elem.nodeType === 1 && !dataPriv.get( elem, "hasDataAttrs" ) ) {
					i = attrs.length;
					while ( i-- ) {

						// Support: IE 11 only
						// The attrs elements can be null (#14894)
						if ( attrs[ i ] ) {
							name = attrs[ i ].name;
							if ( name.indexOf( "data-" ) === 0 ) {
								name = camelCase( name.slice( 5 ) );
								dataAttr( elem, name, data[ name ] );
							}
						}
					}
					dataPriv.set( elem, "hasDataAttrs", true );
				}
			}

			return data;
		}

		// Sets multiple values
		if ( typeof key === "object" ) {
			return this.each( function() {
				dataUser.set( this, key );
			} );
		}

		return access( this, function( value ) {
			var data;

			// The calling jQuery object (element matches) is not empty
			// (and therefore has an element appears at this[ 0 ]) and the
			// `value` parameter was not undefined. An empty jQuery object
			// will result in `undefined` for elem = this[ 0 ] which will
			// throw an exception if an attempt to read a data cache is made.
			if ( elem && value === undefined ) {

				// Attempt to get data from the cache
				// The key will always be camelCased in Data
				data = dataUser.get( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// Attempt to "discover" the data in
				// HTML5 custom data-* attrs
				data = dataAttr( elem, key );
				if ( data !== undefined ) {
					return data;
				}

				// We tried really hard, but the data doesn't exist.
				return;
			}

			// Set the data...
			this.each( function() {

				// We always store the camelCased key
				dataUser.set( this, key, value );
			} );
		}, null, value, arguments.length > 1, null, true );
	},

	removeData: function( key ) {
		return this.each( function() {
			dataUser.remove( this, key );
		} );
	}
} );


jQuery.extend( {
	queue: function( elem, type, data ) {
		var queue;

		if ( elem ) {
			type = ( type || "fx" ) + "queue";
			queue = dataPriv.get( elem, type );

			// Speed up dequeue by getting out quickly if this is just a lookup
			if ( data ) {
				if ( !queue || Array.isArray( data ) ) {
					queue = dataPriv.access( elem, type, jQuery.makeArray( data ) );
				} else {
					queue.push( data );
				}
			}
			return queue || [];
		}
	},

	dequeue: function( elem, type ) {
		type = type || "fx";

		var queue = jQuery.queue( elem, type ),
			startLength = queue.length,
			fn = queue.shift(),
			hooks = jQuery._queueHooks( elem, type ),
			next = function() {
				jQuery.dequeue( elem, type );
			};

		// If the fx queue is dequeued, always remove the progress sentinel
		if ( fn === "inprogress" ) {
			fn = queue.shift();
			startLength--;
		}

		if ( fn ) {

			// Add a progress sentinel to prevent the fx queue from being
			// automatically dequeued
			if ( type === "fx" ) {
				queue.unshift( "inprogress" );
			}

			// Clear up the last queue stop function
			delete hooks.stop;
			fn.call( elem, next, hooks );
		}

		if ( !startLength && hooks ) {
			hooks.empty.fire();
		}
	},

	// Not public - generate a queueHooks object, or return the current one
	_queueHooks: function( elem, type ) {
		var key = type + "queueHooks";
		return dataPriv.get( elem, key ) || dataPriv.access( elem, key, {
			empty: jQuery.Callbacks( "once memory" ).add( function() {
				dataPriv.remove( elem, [ type + "queue", key ] );
			} )
		} );
	}
} );

jQuery.fn.extend( {
	queue: function( type, data ) {
		var setter = 2;

		if ( typeof type !== "string" ) {
			data = type;
			type = "fx";
			setter--;
		}

		if ( arguments.length < setter ) {
			return jQuery.queue( this[ 0 ], type );
		}

		return data === undefined ?
			this :
			this.each( function() {
				var queue = jQuery.queue( this, type, data );

				// Ensure a hooks for this queue
				jQuery._queueHooks( this, type );

				if ( type === "fx" && queue[ 0 ] !== "inprogress" ) {
					jQuery.dequeue( this, type );
				}
			} );
	},
	dequeue: function( type ) {
		return this.each( function() {
			jQuery.dequeue( this, type );
		} );
	},
	clearQueue: function( type ) {
		return this.queue( type || "fx", [] );
	},

	// Get a promise resolved when queues of a certain type
	// are emptied (fx is the type by default)
	promise: function( type, obj ) {
		var tmp,
			count = 1,
			defer = jQuery.Deferred(),
			elements = this,
			i = this.length,
			resolve = function() {
				if ( !( --count ) ) {
					defer.resolveWith( elements, [ elements ] );
				}
			};

		if ( typeof type !== "string" ) {
			obj = type;
			type = undefined;
		}
		type = type || "fx";

		while ( i-- ) {
			tmp = dataPriv.get( elements[ i ], type + "queueHooks" );
			if ( tmp && tmp.empty ) {
				count++;
				tmp.empty.add( resolve );
			}
		}
		resolve();
		return defer.promise( obj );
	}
} );
var pnum = ( /[+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|)/ ).source;

var rcssNum = new RegExp( "^(?:([+-])=|)(" + pnum + ")([a-z%]*)$", "i" );


var cssExpand = [ "Top", "Right", "Bottom", "Left" ];

var documentElement = document.documentElement;



	var isAttached = function( elem ) {
			return jQuery.contains( elem.ownerDocument, elem );
		},
		composed = { composed: true };

	// Support: IE 9 - 11+, Edge 12 - 18+, iOS 10.0 - 10.2 only
	// Check attachment across shadow DOM boundaries when possible (gh-3504)
	// Support: iOS 10.0-10.2 only
	// Early iOS 10 versions support `attachShadow` but not `getRootNode`,
	// leading to errors. We need to check for `getRootNode`.
	if ( documentElement.getRootNode ) {
		isAttached = function( elem ) {
			return jQuery.contains( elem.ownerDocument, elem ) ||
				elem.getRootNode( composed ) === elem.ownerDocument;
		};
	}
var isHiddenWithinTree = function( elem, el ) {

		// isHiddenWithinTree might be called from jQuery#filter function;
		// in that case, element will be second argument
		elem = el || elem;

		// Inline style trumps all
		return elem.style.display === "none" ||
			elem.style.display === "" &&

			// Otherwise, check computed style
			// Support: Firefox <=43 - 45
			// Disconnected elements can have computed display: none, so first confirm that elem is
			// in the document.
			isAttached( elem ) &&

			jQuery.css( elem, "display" ) === "none";
	};



function adjustCSS( elem, prop, valueParts, tween ) {
	var adjusted, scale,
		maxIterations = 20,
		currentValue = tween ?
			function() {
				return tween.cur();
			} :
			function() {
				return jQuery.css( elem, prop, "" );
			},
		initial = currentValue(),
		unit = valueParts && valueParts[ 3 ] || ( jQuery.cssNumber[ prop ] ? "" : "px" ),

		// Starting value computation is required for potential unit mismatches
		initialInUnit = elem.nodeType &&
			( jQuery.cssNumber[ prop ] || unit !== "px" && +initial ) &&
			rcssNum.exec( jQuery.css( elem, prop ) );

	if ( initialInUnit && initialInUnit[ 3 ] !== unit ) {

		// Support: Firefox <=54
		// Halve the iteration target value to prevent interference from CSS upper bounds (gh-2144)
		initial = initial / 2;

		// Trust units reported by jQuery.css
		unit = unit || initialInUnit[ 3 ];

		// Iteratively approximate from a nonzero starting point
		initialInUnit = +initial || 1;

		while ( maxIterations-- ) {

			// Evaluate and update our best guess (doubling guesses that zero out).
			// Finish if the scale equals or crosses 1 (making the old*new product non-positive).
			jQuery.style( elem, prop, initialInUnit + unit );
			if ( ( 1 - scale ) * ( 1 - ( scale = currentValue() / initial || 0.5 ) ) <= 0 ) {
				maxIterations = 0;
			}
			initialInUnit = initialInUnit / scale;

		}

		initialInUnit = initialInUnit * 2;
		jQuery.style( elem, prop, initialInUnit + unit );

		// Make sure we update the tween properties later on
		valueParts = valueParts || [];
	}

	if ( valueParts ) {
		initialInUnit = +initialInUnit || +initial || 0;

		// Apply relative offset (+=/-=) if specified
		adjusted = valueParts[ 1 ] ?
			initialInUnit + ( valueParts[ 1 ] + 1 ) * valueParts[ 2 ] :
			+valueParts[ 2 ];
		if ( tween ) {
			tween.unit = unit;
			tween.start = initialInUnit;
			tween.end = adjusted;
		}
	}
	return adjusted;
}


var defaultDisplayMap = {};

function getDefaultDisplay( elem ) {
	var temp,
		doc = elem.ownerDocument,
		nodeName = elem.nodeName,
		display = defaultDisplayMap[ nodeName ];

	if ( display ) {
		return display;
	}

	temp = doc.body.appendChild( doc.createElement( nodeName ) );
	display = jQuery.css( temp, "display" );

	temp.parentNode.removeChild( temp );

	if ( display === "none" ) {
		display = "block";
	}
	defaultDisplayMap[ nodeName ] = display;

	return display;
}

function showHide( elements, show ) {
	var display, elem,
		values = [],
		index = 0,
		length = elements.length;

	// Determine new display value for elements that need to change
	for ( ; index < length; index++ ) {
		elem = elements[ index ];
		if ( !elem.style ) {
			continue;
		}

		display = elem.style.display;
		if ( show ) {

			// Since we force visibility upon cascade-hidden elements, an immediate (and slow)
			// check is required in this first loop unless we have a nonempty display value (either
			// inline or about-to-be-restored)
			if ( display === "none" ) {
				values[ index ] = dataPriv.get( elem, "display" ) || null;
				if ( !values[ index ] ) {
					elem.style.display = "";
				}
			}
			if ( elem.style.display === "" && isHiddenWithinTree( elem ) ) {
				values[ index ] = getDefaultDisplay( elem );
			}
		} else {
			if ( display !== "none" ) {
				values[ index ] = "none";

				// Remember what we're overwriting
				dataPriv.set( elem, "display", display );
			}
		}
	}

	// Set the display of the elements in a second loop to avoid constant reflow
	for ( index = 0; index < length; index++ ) {
		if ( values[ index ] != null ) {
			elements[ index ].style.display = values[ index ];
		}
	}

	return elements;
}

jQuery.fn.extend( {
	show: function() {
		return showHide( this, true );
	},
	hide: function() {
		return showHide( this );
	},
	toggle: function( state ) {
		if ( typeof state === "boolean" ) {
			return state ? this.show() : this.hide();
		}

		return this.each( function() {
			if ( isHiddenWithinTree( this ) ) {
				jQuery( this ).show();
			} else {
				jQuery( this ).hide();
			}
		} );
	}
} );
var rcheckableType = ( /^(?:checkbox|radio)$/i );

var rtagName = ( /<([a-z][^\/\0>\x20\t\r\n\f]*)/i );

var rscriptType = ( /^$|^module$|\/(?:java|ecma)script/i );



( function() {
	var fragment = document.createDocumentFragment(),
		div = fragment.appendChild( document.createElement( "div" ) ),
		input = document.createElement( "input" );

	// Support: Android 4.0 - 4.3 only
	// Check state lost if the name is set (#11217)
	// Support: Windows Web Apps (WWA)
	// `name` and `type` must use .setAttribute for WWA (#14901)
	input.setAttribute( "type", "radio" );
	input.setAttribute( "checked", "checked" );
	input.setAttribute( "name", "t" );

	div.appendChild( input );

	// Support: Android <=4.1 only
	// Older WebKit doesn't clone checked state correctly in fragments
	support.checkClone = div.cloneNode( true ).cloneNode( true ).lastChild.checked;

	// Support: IE <=11 only
	// Make sure textarea (and checkbox) defaultValue is properly cloned
	div.innerHTML = "<textarea>x</textarea>";
	support.noCloneChecked = !!div.cloneNode( true ).lastChild.defaultValue;

	// Support: IE <=9 only
	// IE <=9 replaces <option> tags with their contents when inserted outside of
	// the select element.
	div.innerHTML = "<option></option>";
	support.option = !!div.lastChild;
} )();


// We have to close these tags to support XHTML (#13200)
var wrapMap = {

	// XHTML parsers do not magically insert elements in the
	// same way that tag soup parsers do. So we cannot shorten
	// this by omitting <tbody> or other required elements.
	thead: [ 1, "<table>", "</table>" ],
	col: [ 2, "<table><colgroup>", "</colgroup></table>" ],
	tr: [ 2, "<table><tbody>", "</tbody></table>" ],
	td: [ 3, "<table><tbody><tr>", "</tr></tbody></table>" ],

	_default: [ 0, "", "" ]
};

wrapMap.tbody = wrapMap.tfoot = wrapMap.colgroup = wrapMap.caption = wrapMap.thead;
wrapMap.th = wrapMap.td;

// Support: IE <=9 only
if ( !support.option ) {
	wrapMap.optgroup = wrapMap.option = [ 1, "<select multiple='multiple'>", "</select>" ];
}


function getAll( context, tag ) {

	// Support: IE <=9 - 11 only
	// Use typeof to avoid zero-argument method invocation on host objects (#15151)
	var ret;

	if ( typeof context.getElementsByTagName !== "undefined" ) {
		ret = context.getElementsByTagName( tag || "*" );

	} else if ( typeof context.querySelectorAll !== "undefined" ) {
		ret = context.querySelectorAll( tag || "*" );

	} else {
		ret = [];
	}

	if ( tag === undefined || tag && nodeName( context, tag ) ) {
		return jQuery.merge( [ context ], ret );
	}

	return ret;
}


// Mark scripts as having already been evaluated
function setGlobalEval( elems, refElements ) {
	var i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		dataPriv.set(
			elems[ i ],
			"globalEval",
			!refElements || dataPriv.get( refElements[ i ], "globalEval" )
		);
	}
}


var rhtml = /<|&#?\w+;/;

function buildFragment( elems, context, scripts, selection, ignored ) {
	var elem, tmp, tag, wrap, attached, j,
		fragment = context.createDocumentFragment(),
		nodes = [],
		i = 0,
		l = elems.length;

	for ( ; i < l; i++ ) {
		elem = elems[ i ];

		if ( elem || elem === 0 ) {

			// Add nodes directly
			if ( toType( elem ) === "object" ) {

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, elem.nodeType ? [ elem ] : elem );

			// Convert non-html into a text node
			} else if ( !rhtml.test( elem ) ) {
				nodes.push( context.createTextNode( elem ) );

			// Convert html into DOM nodes
			} else {
				tmp = tmp || fragment.appendChild( context.createElement( "div" ) );

				// Deserialize a standard representation
				tag = ( rtagName.exec( elem ) || [ "", "" ] )[ 1 ].toLowerCase();
				wrap = wrapMap[ tag ] || wrapMap._default;
				tmp.innerHTML = wrap[ 1 ] + jQuery.htmlPrefilter( elem ) + wrap[ 2 ];

				// Descend through wrappers to the right content
				j = wrap[ 0 ];
				while ( j-- ) {
					tmp = tmp.lastChild;
				}

				// Support: Android <=4.0 only, PhantomJS 1 only
				// push.apply(_, arraylike) throws on ancient WebKit
				jQuery.merge( nodes, tmp.childNodes );

				// Remember the top-level container
				tmp = fragment.firstChild;

				// Ensure the created nodes are orphaned (#12392)
				tmp.textContent = "";
			}
		}
	}

	// Remove wrapper from fragment
	fragment.textContent = "";

	i = 0;
	while ( ( elem = nodes[ i++ ] ) ) {

		// Skip elements already in the context collection (trac-4087)
		if ( selection && jQuery.inArray( elem, selection ) > -1 ) {
			if ( ignored ) {
				ignored.push( elem );
			}
			continue;
		}

		attached = isAttached( elem );

		// Append to fragment
		tmp = getAll( fragment.appendChild( elem ), "script" );

		// Preserve script evaluation history
		if ( attached ) {
			setGlobalEval( tmp );
		}

		// Capture executables
		if ( scripts ) {
			j = 0;
			while ( ( elem = tmp[ j++ ] ) ) {
				if ( rscriptType.test( elem.type || "" ) ) {
					scripts.push( elem );
				}
			}
		}
	}

	return fragment;
}


var
	rkeyEvent = /^key/,
	rmouseEvent = /^(?:mouse|pointer|contextmenu|drag|drop)|click/,
	rtypenamespace = /^([^.]*)(?:\.(.+)|)/;

function returnTrue() {
	return true;
}

function returnFalse() {
	return false;
}

// Support: IE <=9 - 11+
// focus() and blur() are asynchronous, except when they are no-op.
// So expect focus to be synchronous when the element is already active,
// and blur to be synchronous when the element is not already active.
// (focus and blur are always synchronous in other supported browsers,
// this just defines when we can count on it).
function expectSync( elem, type ) {
	return ( elem === safeActiveElement() ) === ( type === "focus" );
}

// Support: IE <=9 only
// Accessing document.activeElement can throw unexpectedly
// https://bugs.jquery.com/ticket/13393
function safeActiveElement() {
	try {
		return document.activeElement;
	} catch ( err ) { }
}

function on( elem, types, selector, data, fn, one ) {
	var origFn, type;

	// Types can be a map of types/handlers
	if ( typeof types === "object" ) {

		// ( types-Object, selector, data )
		if ( typeof selector !== "string" ) {

			// ( types-Object, data )
			data = data || selector;
			selector = undefined;
		}
		for ( type in types ) {
			on( elem, type, selector, data, types[ type ], one );
		}
		return elem;
	}

	if ( data == null && fn == null ) {

		// ( types, fn )
		fn = selector;
		data = selector = undefined;
	} else if ( fn == null ) {
		if ( typeof selector === "string" ) {

			// ( types, selector, fn )
			fn = data;
			data = undefined;
		} else {

			// ( types, data, fn )
			fn = data;
			data = selector;
			selector = undefined;
		}
	}
	if ( fn === false ) {
		fn = returnFalse;
	} else if ( !fn ) {
		return elem;
	}

	if ( one === 1 ) {
		origFn = fn;
		fn = function( event ) {

			// Can use an empty set, since event contains the info
			jQuery().off( event );
			return origFn.apply( this, arguments );
		};

		// Use same guid so caller can remove using origFn
		fn.guid = origFn.guid || ( origFn.guid = jQuery.guid++ );
	}
	return elem.each( function() {
		jQuery.event.add( this, types, fn, data, selector );
	} );
}

/*
 * Helper functions for managing events -- not part of the public interface.
 * Props to Dean Edwards' addEvent library for many of the ideas.
 */
jQuery.event = {

	global: {},

	add: function( elem, types, handler, data, selector ) {

		var handleObjIn, eventHandle, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.get( elem );

		// Only attach events to objects that accept data
		if ( !acceptData( elem ) ) {
			return;
		}

		// Caller can pass in an object of custom data in lieu of the handler
		if ( handler.handler ) {
			handleObjIn = handler;
			handler = handleObjIn.handler;
			selector = handleObjIn.selector;
		}

		// Ensure that invalid selectors throw exceptions at attach time
		// Evaluate against documentElement in case elem is a non-element node (e.g., document)
		if ( selector ) {
			jQuery.find.matchesSelector( documentElement, selector );
		}

		// Make sure that the handler has a unique ID, used to find/remove it later
		if ( !handler.guid ) {
			handler.guid = jQuery.guid++;
		}

		// Init the element's event structure and main handler, if this is the first
		if ( !( events = elemData.events ) ) {
			events = elemData.events = Object.create( null );
		}
		if ( !( eventHandle = elemData.handle ) ) {
			eventHandle = elemData.handle = function( e ) {

				// Discard the second event of a jQuery.event.trigger() and
				// when an event is called after a page has unloaded
				return typeof jQuery !== "undefined" && jQuery.event.triggered !== e.type ?
					jQuery.event.dispatch.apply( elem, arguments ) : undefined;
			};
		}

		// Handle multiple events separated by a space
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// There *must* be a type, no attaching namespace-only handlers
			if ( !type ) {
				continue;
			}

			// If event changes its type, use the special event handlers for the changed type
			special = jQuery.event.special[ type ] || {};

			// If selector defined, determine special event api type, otherwise given type
			type = ( selector ? special.delegateType : special.bindType ) || type;

			// Update special based on newly reset type
			special = jQuery.event.special[ type ] || {};

			// handleObj is passed to all event handlers
			handleObj = jQuery.extend( {
				type: type,
				origType: origType,
				data: data,
				handler: handler,
				guid: handler.guid,
				selector: selector,
				needsContext: selector && jQuery.expr.match.needsContext.test( selector ),
				namespace: namespaces.join( "." )
			}, handleObjIn );

			// Init the event handler queue if we're the first
			if ( !( handlers = events[ type ] ) ) {
				handlers = events[ type ] = [];
				handlers.delegateCount = 0;

				// Only use addEventListener if the special events handler returns false
				if ( !special.setup ||
					special.setup.call( elem, data, namespaces, eventHandle ) === false ) {

					if ( elem.addEventListener ) {
						elem.addEventListener( type, eventHandle );
					}
				}
			}

			if ( special.add ) {
				special.add.call( elem, handleObj );

				if ( !handleObj.handler.guid ) {
					handleObj.handler.guid = handler.guid;
				}
			}

			// Add to the element's handler list, delegates in front
			if ( selector ) {
				handlers.splice( handlers.delegateCount++, 0, handleObj );
			} else {
				handlers.push( handleObj );
			}

			// Keep track of which events have ever been used, for event optimization
			jQuery.event.global[ type ] = true;
		}

	},

	// Detach an event or set of events from an element
	remove: function( elem, types, handler, selector, mappedTypes ) {

		var j, origCount, tmp,
			events, t, handleObj,
			special, handlers, type, namespaces, origType,
			elemData = dataPriv.hasData( elem ) && dataPriv.get( elem );

		if ( !elemData || !( events = elemData.events ) ) {
			return;
		}

		// Once for each type.namespace in types; type may be omitted
		types = ( types || "" ).match( rnothtmlwhite ) || [ "" ];
		t = types.length;
		while ( t-- ) {
			tmp = rtypenamespace.exec( types[ t ] ) || [];
			type = origType = tmp[ 1 ];
			namespaces = ( tmp[ 2 ] || "" ).split( "." ).sort();

			// Unbind all events (on this namespace, if provided) for the element
			if ( !type ) {
				for ( type in events ) {
					jQuery.event.remove( elem, type + types[ t ], handler, selector, true );
				}
				continue;
			}

			special = jQuery.event.special[ type ] || {};
			type = ( selector ? special.delegateType : special.bindType ) || type;
			handlers = events[ type ] || [];
			tmp = tmp[ 2 ] &&
				new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" );

			// Remove matching events
			origCount = j = handlers.length;
			while ( j-- ) {
				handleObj = handlers[ j ];

				if ( ( mappedTypes || origType === handleObj.origType ) &&
					( !handler || handler.guid === handleObj.guid ) &&
					( !tmp || tmp.test( handleObj.namespace ) ) &&
					( !selector || selector === handleObj.selector ||
						selector === "**" && handleObj.selector ) ) {
					handlers.splice( j, 1 );

					if ( handleObj.selector ) {
						handlers.delegateCount--;
					}
					if ( special.remove ) {
						special.remove.call( elem, handleObj );
					}
				}
			}

			// Remove generic event handler if we removed something and no more handlers exist
			// (avoids potential for endless recursion during removal of special event handlers)
			if ( origCount && !handlers.length ) {
				if ( !special.teardown ||
					special.teardown.call( elem, namespaces, elemData.handle ) === false ) {

					jQuery.removeEvent( elem, type, elemData.handle );
				}

				delete events[ type ];
			}
		}

		// Remove data and the expando if it's no longer used
		if ( jQuery.isEmptyObject( events ) ) {
			dataPriv.remove( elem, "handle events" );
		}
	},

	dispatch: function( nativeEvent ) {

		var i, j, ret, matched, handleObj, handlerQueue,
			args = new Array( arguments.length ),

			// Make a writable jQuery.Event from the native event object
			event = jQuery.event.fix( nativeEvent ),

			handlers = (
					dataPriv.get( this, "events" ) || Object.create( null )
				)[ event.type ] || [],
			special = jQuery.event.special[ event.type ] || {};

		// Use the fix-ed jQuery.Event rather than the (read-only) native event
		args[ 0 ] = event;

		for ( i = 1; i < arguments.length; i++ ) {
			args[ i ] = arguments[ i ];
		}

		event.delegateTarget = this;

		// Call the preDispatch hook for the mapped type, and let it bail if desired
		if ( special.preDispatch && special.preDispatch.call( this, event ) === false ) {
			return;
		}

		// Determine handlers
		handlerQueue = jQuery.event.handlers.call( this, event, handlers );

		// Run delegates first; they may want to stop propagation beneath us
		i = 0;
		while ( ( matched = handlerQueue[ i++ ] ) && !event.isPropagationStopped() ) {
			event.currentTarget = matched.elem;

			j = 0;
			while ( ( handleObj = matched.handlers[ j++ ] ) &&
				!event.isImmediatePropagationStopped() ) {

				// If the event is namespaced, then each handler is only invoked if it is
				// specially universal or its namespaces are a superset of the event's.
				if ( !event.rnamespace || handleObj.namespace === false ||
					event.rnamespace.test( handleObj.namespace ) ) {

					event.handleObj = handleObj;
					event.data = handleObj.data;

					ret = ( ( jQuery.event.special[ handleObj.origType ] || {} ).handle ||
						handleObj.handler ).apply( matched.elem, args );

					if ( ret !== undefined ) {
						if ( ( event.result = ret ) === false ) {
							event.preventDefault();
							event.stopPropagation();
						}
					}
				}
			}
		}

		// Call the postDispatch hook for the mapped type
		if ( special.postDispatch ) {
			special.postDispatch.call( this, event );
		}

		return event.result;
	},

	handlers: function( event, handlers ) {
		var i, handleObj, sel, matchedHandlers, matchedSelectors,
			handlerQueue = [],
			delegateCount = handlers.delegateCount,
			cur = event.target;

		// Find delegate handlers
		if ( delegateCount &&

			// Support: IE <=9
			// Black-hole SVG <use> instance trees (trac-13180)
			cur.nodeType &&

			// Support: Firefox <=42
			// Suppress spec-violating clicks indicating a non-primary pointer button (trac-3861)
			// https://www.w3.org/TR/DOM-Level-3-Events/#event-type-click
			// Support: IE 11 only
			// ...but not arrow key "clicks" of radio inputs, which can have `button` -1 (gh-2343)
			!( event.type === "click" && event.button >= 1 ) ) {

			for ( ; cur !== this; cur = cur.parentNode || this ) {

				// Don't check non-elements (#13208)
				// Don't process clicks on disabled elements (#6911, #8165, #11382, #11764)
				if ( cur.nodeType === 1 && !( event.type === "click" && cur.disabled === true ) ) {
					matchedHandlers = [];
					matchedSelectors = {};
					for ( i = 0; i < delegateCount; i++ ) {
						handleObj = handlers[ i ];

						// Don't conflict with Object.prototype properties (#13203)
						sel = handleObj.selector + " ";

						if ( matchedSelectors[ sel ] === undefined ) {
							matchedSelectors[ sel ] = handleObj.needsContext ?
								jQuery( sel, this ).index( cur ) > -1 :
								jQuery.find( sel, this, null, [ cur ] ).length;
						}
						if ( matchedSelectors[ sel ] ) {
							matchedHandlers.push( handleObj );
						}
					}
					if ( matchedHandlers.length ) {
						handlerQueue.push( { elem: cur, handlers: matchedHandlers } );
					}
				}
			}
		}

		// Add the remaining (directly-bound) handlers
		cur = this;
		if ( delegateCount < handlers.length ) {
			handlerQueue.push( { elem: cur, handlers: handlers.slice( delegateCount ) } );
		}

		return handlerQueue;
	},

	addProp: function( name, hook ) {
		Object.defineProperty( jQuery.Event.prototype, name, {
			enumerable: true,
			configurable: true,

			get: isFunction( hook ) ?
				function() {
					if ( this.originalEvent ) {
							return hook( this.originalEvent );
					}
				} :
				function() {
					if ( this.originalEvent ) {
							return this.originalEvent[ name ];
					}
				},

			set: function( value ) {
				Object.defineProperty( this, name, {
					enumerable: true,
					configurable: true,
					writable: true,
					value: value
				} );
			}
		} );
	},

	fix: function( originalEvent ) {
		return originalEvent[ jQuery.expando ] ?
			originalEvent :
			new jQuery.Event( originalEvent );
	},

	special: {
		load: {

			// Prevent triggered image.load events from bubbling to window.load
			noBubble: true
		},
		click: {

			// Utilize native event to ensure correct state for checkable inputs
			setup: function( data ) {

				// For mutual compressibility with _default, replace `this` access with a local var.
				// `|| data` is dead code meant only to preserve the variable through minification.
				var el = this || data;

				// Claim the first handler
				if ( rcheckableType.test( el.type ) &&
					el.click && nodeName( el, "input" ) ) {

					// dataPriv.set( el, "click", ... )
					leverageNative( el, "click", returnTrue );
				}

				// Return false to allow normal processing in the caller
				return false;
			},
			trigger: function( data ) {

				// For mutual compressibility with _default, replace `this` access with a local var.
				// `|| data` is dead code meant only to preserve the variable through minification.
				var el = this || data;

				// Force setup before triggering a click
				if ( rcheckableType.test( el.type ) &&
					el.click && nodeName( el, "input" ) ) {

					leverageNative( el, "click" );
				}

				// Return non-false to allow normal event-path propagation
				return true;
			},

			// For cross-browser consistency, suppress native .click() on links
			// Also prevent it if we're currently inside a leveraged native-event stack
			_default: function( event ) {
				var target = event.target;
				return rcheckableType.test( target.type ) &&
					target.click && nodeName( target, "input" ) &&
					dataPriv.get( target, "click" ) ||
					nodeName( target, "a" );
			}
		},

		beforeunload: {
			postDispatch: function( event ) {

				// Support: Firefox 20+
				// Firefox doesn't alert if the returnValue field is not set.
				if ( event.result !== undefined && event.originalEvent ) {
					event.originalEvent.returnValue = event.result;
				}
			}
		}
	}
};

// Ensure the presence of an event listener that handles manually-triggered
// synthetic events by interrupting progress until reinvoked in response to
// *native* events that it fires directly, ensuring that state changes have
// already occurred before other listeners are invoked.
function leverageNative( el, type, expectSync ) {

	// Missing expectSync indicates a trigger call, which must force setup through jQuery.event.add
	if ( !expectSync ) {
		if ( dataPriv.get( el, type ) === undefined ) {
			jQuery.event.add( el, type, returnTrue );
		}
		return;
	}

	// Register the controller as a special universal handler for all event namespaces
	dataPriv.set( el, type, false );
	jQuery.event.add( el, type, {
		namespace: false,
		handler: function( event ) {
			var notAsync, result,
				saved = dataPriv.get( this, type );

			if ( ( event.isTrigger & 1 ) && this[ type ] ) {

				// Interrupt processing of the outer synthetic .trigger()ed event
				// Saved data should be false in such cases, but might be a leftover capture object
				// from an async native handler (gh-4350)
				if ( !saved.length ) {

					// Store arguments for use when handling the inner native event
					// There will always be at least one argument (an event object), so this array
					// will not be confused with a leftover capture object.
					saved = slice.call( arguments );
					dataPriv.set( this, type, saved );

					// Trigger the native event and capture its result
					// Support: IE <=9 - 11+
					// focus() and blur() are asynchronous
					notAsync = expectSync( this, type );
					this[ type ]();
					result = dataPriv.get( this, type );
					if ( saved !== result || notAsync ) {
						dataPriv.set( this, type, false );
					} else {
						result = {};
					}
					if ( saved !== result ) {

						// Cancel the outer synthetic event
						event.stopImmediatePropagation();
						event.preventDefault();
						return result.value;
					}

				// If this is an inner synthetic event for an event with a bubbling surrogate
				// (focus or blur), assume that the surrogate already propagated from triggering the
				// native event and prevent that from happening again here.
				// This technically gets the ordering wrong w.r.t. to `.trigger()` (in which the
				// bubbling surrogate propagates *after* the non-bubbling base), but that seems
				// less bad than duplication.
				} else if ( ( jQuery.event.special[ type ] || {} ).delegateType ) {
					event.stopPropagation();
				}

			// If this is a native event triggered above, everything is now in order
			// Fire an inner synthetic event with the original arguments
			} else if ( saved.length ) {

				// ...and capture the result
				dataPriv.set( this, type, {
					value: jQuery.event.trigger(

						// Support: IE <=9 - 11+
						// Extend with the prototype to reset the above stopImmediatePropagation()
						jQuery.extend( saved[ 0 ], jQuery.Event.prototype ),
						saved.slice( 1 ),
						this
					)
				} );

				// Abort handling of the native event
				event.stopImmediatePropagation();
			}
		}
	} );
}

jQuery.removeEvent = function( elem, type, handle ) {

	// This "if" is needed for plain objects
	if ( elem.removeEventListener ) {
		elem.removeEventListener( type, handle );
	}
};

jQuery.Event = function( src, props ) {

	// Allow instantiation without the 'new' keyword
	if ( !( this instanceof jQuery.Event ) ) {
		return new jQuery.Event( src, props );
	}

	// Event object
	if ( src && src.type ) {
		this.originalEvent = src;
		this.type = src.type;

		// Events bubbling up the document may have been marked as prevented
		// by a handler lower down the tree; reflect the correct value.
		this.isDefaultPrevented = src.defaultPrevented ||
				src.defaultPrevented === undefined &&

				// Support: Android <=2.3 only
				src.returnValue === false ?
			returnTrue :
			returnFalse;

		// Create target properties
		// Support: Safari <=6 - 7 only
		// Target should not be a text node (#504, #13143)
		this.target = ( src.target && src.target.nodeType === 3 ) ?
			src.target.parentNode :
			src.target;

		this.currentTarget = src.currentTarget;
		this.relatedTarget = src.relatedTarget;

	// Event type
	} else {
		this.type = src;
	}

	// Put explicitly provided properties onto the event object
	if ( props ) {
		jQuery.extend( this, props );
	}

	// Create a timestamp if incoming event doesn't have one
	this.timeStamp = src && src.timeStamp || Date.now();

	// Mark it as fixed
	this[ jQuery.expando ] = true;
};

// jQuery.Event is based on DOM3 Events as specified by the ECMAScript Language Binding
// https://www.w3.org/TR/2003/WD-DOM-Level-3-Events-20030331/ecma-script-binding.html
jQuery.Event.prototype = {
	constructor: jQuery.Event,
	isDefaultPrevented: returnFalse,
	isPropagationStopped: returnFalse,
	isImmediatePropagationStopped: returnFalse,
	isSimulated: false,

	preventDefault: function() {
		var e = this.originalEvent;

		this.isDefaultPrevented = returnTrue;

		if ( e && !this.isSimulated ) {
			e.preventDefault();
		}
	},
	stopPropagation: function() {
		var e = this.originalEvent;

		this.isPropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopPropagation();
		}
	},
	stopImmediatePropagation: function() {
		var e = this.originalEvent;

		this.isImmediatePropagationStopped = returnTrue;

		if ( e && !this.isSimulated ) {
			e.stopImmediatePropagation();
		}

		this.stopPropagation();
	}
};

// Includes all common event props including KeyEvent and MouseEvent specific props
jQuery.each( {
	altKey: true,
	bubbles: true,
	cancelable: true,
	changedTouches: true,
	ctrlKey: true,
	detail: true,
	eventPhase: true,
	metaKey: true,
	pageX: true,
	pageY: true,
	shiftKey: true,
	view: true,
	"char": true,
	code: true,
	charCode: true,
	key: true,
	keyCode: true,
	button: true,
	buttons: true,
	clientX: true,
	clientY: true,
	offsetX: true,
	offsetY: true,
	pointerId: true,
	pointerType: true,
	screenX: true,
	screenY: true,
	targetTouches: true,
	toElement: true,
	touches: true,

	which: function( event ) {
		var button = event.button;

		// Add which for key events
		if ( event.which == null && rkeyEvent.test( event.type ) ) {
			return event.charCode != null ? event.charCode : event.keyCode;
		}

		// Add which for click: 1 === left; 2 === middle; 3 === right
		if ( !event.which && button !== undefined && rmouseEvent.test( event.type ) ) {
			if ( button & 1 ) {
				return 1;
			}

			if ( button & 2 ) {
				return 3;
			}

			if ( button & 4 ) {
				return 2;
			}

			return 0;
		}

		return event.which;
	}
}, jQuery.event.addProp );

jQuery.each( { focus: "focusin", blur: "focusout" }, function( type, delegateType ) {
	jQuery.event.special[ type ] = {

		// Utilize native event if possible so blur/focus sequence is correct
		setup: function() {

			// Claim the first handler
			// dataPriv.set( this, "focus", ... )
			// dataPriv.set( this, "blur", ... )
			leverageNative( this, type, expectSync );

			// Return false to allow normal processing in the caller
			return false;
		},
		trigger: function() {

			// Force setup before trigger
			leverageNative( this, type );

			// Return non-false to allow normal event-path propagation
			return true;
		},

		delegateType: delegateType
	};
} );

// Create mouseenter/leave events using mouseover/out and event-time checks
// so that event delegation works in jQuery.
// Do the same for pointerenter/pointerleave and pointerover/pointerout
//
// Support: Safari 7 only
// Safari sends mouseenter too often; see:
// https://bugs.chromium.org/p/chromium/issues/detail?id=470258
// for the description of the bug (it existed in older Chrome versions as well).
jQuery.each( {
	mouseenter: "mouseover",
	mouseleave: "mouseout",
	pointerenter: "pointerover",
	pointerleave: "pointerout"
}, function( orig, fix ) {
	jQuery.event.special[ orig ] = {
		delegateType: fix,
		bindType: fix,

		handle: function( event ) {
			var ret,
				target = this,
				related = event.relatedTarget,
				handleObj = event.handleObj;

			// For mouseenter/leave call the handler if related is outside the target.
			// NB: No relatedTarget if the mouse left/entered the browser window
			if ( !related || ( related !== target && !jQuery.contains( target, related ) ) ) {
				event.type = handleObj.origType;
				ret = handleObj.handler.apply( this, arguments );
				event.type = fix;
			}
			return ret;
		}
	};
} );

jQuery.fn.extend( {

	on: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn );
	},
	one: function( types, selector, data, fn ) {
		return on( this, types, selector, data, fn, 1 );
	},
	off: function( types, selector, fn ) {
		var handleObj, type;
		if ( types && types.preventDefault && types.handleObj ) {

			// ( event )  dispatched jQuery.Event
			handleObj = types.handleObj;
			jQuery( types.delegateTarget ).off(
				handleObj.namespace ?
					handleObj.origType + "." + handleObj.namespace :
					handleObj.origType,
				handleObj.selector,
				handleObj.handler
			);
			return this;
		}
		if ( typeof types === "object" ) {

			// ( types-object [, selector] )
			for ( type in types ) {
				this.off( type, selector, types[ type ] );
			}
			return this;
		}
		if ( selector === false || typeof selector === "function" ) {

			// ( types [, fn] )
			fn = selector;
			selector = undefined;
		}
		if ( fn === false ) {
			fn = returnFalse;
		}
		return this.each( function() {
			jQuery.event.remove( this, types, fn, selector );
		} );
	}
} );


var

	// Support: IE <=10 - 11, Edge 12 - 13 only
	// In IE/Edge using regex groups here causes severe slowdowns.
	// See https://connect.microsoft.com/IE/feedback/details/1736512/
	rnoInnerhtml = /<script|<style|<link/i,

	// checked="checked" or checked
	rchecked = /checked\s*(?:[^=]|=\s*.checked.)/i,
	rcleanScript = /^\s*<!(?:\[CDATA\[|--)|(?:\]\]|--)>\s*$/g;

// Prefer a tbody over its parent table for containing new rows
function manipulationTarget( elem, content ) {
	if ( nodeName( elem, "table" ) &&
		nodeName( content.nodeType !== 11 ? content : content.firstChild, "tr" ) ) {

		return jQuery( elem ).children( "tbody" )[ 0 ] || elem;
	}

	return elem;
}

// Replace/restore the type attribute of script elements for safe DOM manipulation
function disableScript( elem ) {
	elem.type = ( elem.getAttribute( "type" ) !== null ) + "/" + elem.type;
	return elem;
}
function restoreScript( elem ) {
	if ( ( elem.type || "" ).slice( 0, 5 ) === "true/" ) {
		elem.type = elem.type.slice( 5 );
	} else {
		elem.removeAttribute( "type" );
	}

	return elem;
}

function cloneCopyEvent( src, dest ) {
	var i, l, type, pdataOld, udataOld, udataCur, events;

	if ( dest.nodeType !== 1 ) {
		return;
	}

	// 1. Copy private data: events, handlers, etc.
	if ( dataPriv.hasData( src ) ) {
		pdataOld = dataPriv.get( src );
		events = pdataOld.events;

		if ( events ) {
			dataPriv.remove( dest, "handle events" );

			for ( type in events ) {
				for ( i = 0, l = events[ type ].length; i < l; i++ ) {
					jQuery.event.add( dest, type, events[ type ][ i ] );
				}
			}
		}
	}

	// 2. Copy user data
	if ( dataUser.hasData( src ) ) {
		udataOld = dataUser.access( src );
		udataCur = jQuery.extend( {}, udataOld );

		dataUser.set( dest, udataCur );
	}
}

// Fix IE bugs, see support tests
function fixInput( src, dest ) {
	var nodeName = dest.nodeName.toLowerCase();

	// Fails to persist the checked state of a cloned checkbox or radio button.
	if ( nodeName === "input" && rcheckableType.test( src.type ) ) {
		dest.checked = src.checked;

	// Fails to return the selected option to the default selected state when cloning options
	} else if ( nodeName === "input" || nodeName === "textarea" ) {
		dest.defaultValue = src.defaultValue;
	}
}

function domManip( collection, args, callback, ignored ) {

	// Flatten any nested arrays
	args = flat( args );

	var fragment, first, scripts, hasScripts, node, doc,
		i = 0,
		l = collection.length,
		iNoClone = l - 1,
		value = args[ 0 ],
		valueIsFunction = isFunction( value );

	// We can't cloneNode fragments that contain checked, in WebKit
	if ( valueIsFunction ||
			( l > 1 && typeof value === "string" &&
				!support.checkClone && rchecked.test( value ) ) ) {
		return collection.each( function( index ) {
			var self = collection.eq( index );
			if ( valueIsFunction ) {
				args[ 0 ] = value.call( this, index, self.html() );
			}
			domManip( self, args, callback, ignored );
		} );
	}

	if ( l ) {
		fragment = buildFragment( args, collection[ 0 ].ownerDocument, false, collection, ignored );
		first = fragment.firstChild;

		if ( fragment.childNodes.length === 1 ) {
			fragment = first;
		}

		// Require either new content or an interest in ignored elements to invoke the callback
		if ( first || ignored ) {
			scripts = jQuery.map( getAll( fragment, "script" ), disableScript );
			hasScripts = scripts.length;

			// Use the original fragment for the last item
			// instead of the first because it can end up
			// being emptied incorrectly in certain situations (#8070).
			for ( ; i < l; i++ ) {
				node = fragment;

				if ( i !== iNoClone ) {
					node = jQuery.clone( node, true, true );

					// Keep references to cloned scripts for later restoration
					if ( hasScripts ) {

						// Support: Android <=4.0 only, PhantomJS 1 only
						// push.apply(_, arraylike) throws on ancient WebKit
						jQuery.merge( scripts, getAll( node, "script" ) );
					}
				}

				callback.call( collection[ i ], node, i );
			}

			if ( hasScripts ) {
				doc = scripts[ scripts.length - 1 ].ownerDocument;

				// Reenable scripts
				jQuery.map( scripts, restoreScript );

				// Evaluate executable scripts on first document insertion
				for ( i = 0; i < hasScripts; i++ ) {
					node = scripts[ i ];
					if ( rscriptType.test( node.type || "" ) &&
						!dataPriv.access( node, "globalEval" ) &&
						jQuery.contains( doc, node ) ) {

						if ( node.src && ( node.type || "" ).toLowerCase()  !== "module" ) {

							// Optional AJAX dependency, but won't run scripts if not present
							if ( jQuery._evalUrl && !node.noModule ) {
								jQuery._evalUrl( node.src, {
									nonce: node.nonce || node.getAttribute( "nonce" )
								}, doc );
							}
						} else {
							DOMEval( node.textContent.replace( rcleanScript, "" ), node, doc );
						}
					}
				}
			}
		}
	}

	return collection;
}

function remove( elem, selector, keepData ) {
	var node,
		nodes = selector ? jQuery.filter( selector, elem ) : elem,
		i = 0;

	for ( ; ( node = nodes[ i ] ) != null; i++ ) {
		if ( !keepData && node.nodeType === 1 ) {
			jQuery.cleanData( getAll( node ) );
		}

		if ( node.parentNode ) {
			if ( keepData && isAttached( node ) ) {
				setGlobalEval( getAll( node, "script" ) );
			}
			node.parentNode.removeChild( node );
		}
	}

	return elem;
}

jQuery.extend( {
	htmlPrefilter: function( html ) {
		return html;
	},

	clone: function( elem, dataAndEvents, deepDataAndEvents ) {
		var i, l, srcElements, destElements,
			clone = elem.cloneNode( true ),
			inPage = isAttached( elem );

		// Fix IE cloning issues
		if ( !support.noCloneChecked && ( elem.nodeType === 1 || elem.nodeType === 11 ) &&
				!jQuery.isXMLDoc( elem ) ) {

			// We eschew Sizzle here for performance reasons: https://jsperf.com/getall-vs-sizzle/2
			destElements = getAll( clone );
			srcElements = getAll( elem );

			for ( i = 0, l = srcElements.length; i < l; i++ ) {
				fixInput( srcElements[ i ], destElements[ i ] );
			}
		}

		// Copy the events from the original to the clone
		if ( dataAndEvents ) {
			if ( deepDataAndEvents ) {
				srcElements = srcElements || getAll( elem );
				destElements = destElements || getAll( clone );

				for ( i = 0, l = srcElements.length; i < l; i++ ) {
					cloneCopyEvent( srcElements[ i ], destElements[ i ] );
				}
			} else {
				cloneCopyEvent( elem, clone );
			}
		}

		// Preserve script evaluation history
		destElements = getAll( clone, "script" );
		if ( destElements.length > 0 ) {
			setGlobalEval( destElements, !inPage && getAll( elem, "script" ) );
		}

		// Return the cloned set
		return clone;
	},

	cleanData: function( elems ) {
		var data, elem, type,
			special = jQuery.event.special,
			i = 0;

		for ( ; ( elem = elems[ i ] ) !== undefined; i++ ) {
			if ( acceptData( elem ) ) {
				if ( ( data = elem[ dataPriv.expando ] ) ) {
					if ( data.events ) {
						for ( type in data.events ) {
							if ( special[ type ] ) {
								jQuery.event.remove( elem, type );

							// This is a shortcut to avoid jQuery.event.remove's overhead
							} else {
								jQuery.removeEvent( elem, type, data.handle );
							}
						}
					}

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataPriv.expando ] = undefined;
				}
				if ( elem[ dataUser.expando ] ) {

					// Support: Chrome <=35 - 45+
					// Assign undefined instead of using delete, see Data#remove
					elem[ dataUser.expando ] = undefined;
				}
			}
		}
	}
} );

jQuery.fn.extend( {
	detach: function( selector ) {
		return remove( this, selector, true );
	},

	remove: function( selector ) {
		return remove( this, selector );
	},

	text: function( value ) {
		return access( this, function( value ) {
			return value === undefined ?
				jQuery.text( this ) :
				this.empty().each( function() {
					if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
						this.textContent = value;
					}
				} );
		}, null, value, arguments.length );
	},

	append: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.appendChild( elem );
			}
		} );
	},

	prepend: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.nodeType === 1 || this.nodeType === 11 || this.nodeType === 9 ) {
				var target = manipulationTarget( this, elem );
				target.insertBefore( elem, target.firstChild );
			}
		} );
	},

	before: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this );
			}
		} );
	},

	after: function() {
		return domManip( this, arguments, function( elem ) {
			if ( this.parentNode ) {
				this.parentNode.insertBefore( elem, this.nextSibling );
			}
		} );
	},

	empty: function() {
		var elem,
			i = 0;

		for ( ; ( elem = this[ i ] ) != null; i++ ) {
			if ( elem.nodeType === 1 ) {

				// Prevent memory leaks
				jQuery.cleanData( getAll( elem, false ) );

				// Remove any remaining nodes
				elem.textContent = "";
			}
		}

		return this;
	},

	clone: function( dataAndEvents, deepDataAndEvents ) {
		dataAndEvents = dataAndEvents == null ? false : dataAndEvents;
		deepDataAndEvents = deepDataAndEvents == null ? dataAndEvents : deepDataAndEvents;

		return this.map( function() {
			return jQuery.clone( this, dataAndEvents, deepDataAndEvents );
		} );
	},

	html: function( value ) {
		return access( this, function( value ) {
			var elem = this[ 0 ] || {},
				i = 0,
				l = this.length;

			if ( value === undefined && elem.nodeType === 1 ) {
				return elem.innerHTML;
			}

			// See if we can take a shortcut and just use innerHTML
			if ( typeof value === "string" && !rnoInnerhtml.test( value ) &&
				!wrapMap[ ( rtagName.exec( value ) || [ "", "" ] )[ 1 ].toLowerCase() ] ) {

				value = jQuery.htmlPrefilter( value );

				try {
					for ( ; i < l; i++ ) {
						elem = this[ i ] || {};

						// Remove element nodes and prevent memory leaks
						if ( elem.nodeType === 1 ) {
							jQuery.cleanData( getAll( elem, false ) );
							elem.innerHTML = value;
						}
					}

					elem = 0;

				// If using innerHTML throws an exception, use the fallback method
				} catch ( e ) {}
			}

			if ( elem ) {
				this.empty().append( value );
			}
		}, null, value, arguments.length );
	},

	replaceWith: function() {
		var ignored = [];

		// Make the changes, replacing each non-ignored context element with the new content
		return domManip( this, arguments, function( elem ) {
			var parent = this.parentNode;

			if ( jQuery.inArray( this, ignored ) < 0 ) {
				jQuery.cleanData( getAll( this ) );
				if ( parent ) {
					parent.replaceChild( elem, this );
				}
			}

		// Force callback invocation
		}, ignored );
	}
} );

jQuery.each( {
	appendTo: "append",
	prependTo: "prepend",
	insertBefore: "before",
	insertAfter: "after",
	replaceAll: "replaceWith"
}, function( name, original ) {
	jQuery.fn[ name ] = function( selector ) {
		var elems,
			ret = [],
			insert = jQuery( selector ),
			last = insert.length - 1,
			i = 0;

		for ( ; i <= last; i++ ) {
			elems = i === last ? this : this.clone( true );
			jQuery( insert[ i ] )[ original ]( elems );

			// Support: Android <=4.0 only, PhantomJS 1 only
			// .get() because push.apply(_, arraylike) throws on ancient WebKit
			push.apply( ret, elems.get() );
		}

		return this.pushStack( ret );
	};
} );
var rnumnonpx = new RegExp( "^(" + pnum + ")(?!px)[a-z%]+$", "i" );

var getStyles = function( elem ) {

		// Support: IE <=11 only, Firefox <=30 (#15098, #14150)
		// IE throws on elements created in popups
		// FF meanwhile throws on frame elements through "defaultView.getComputedStyle"
		var view = elem.ownerDocument.defaultView;

		if ( !view || !view.opener ) {
			view = window;
		}

		return view.getComputedStyle( elem );
	};

var swap = function( elem, options, callback ) {
	var ret, name,
		old = {};

	// Remember the old values, and insert the new ones
	for ( name in options ) {
		old[ name ] = elem.style[ name ];
		elem.style[ name ] = options[ name ];
	}

	ret = callback.call( elem );

	// Revert the old values
	for ( name in options ) {
		elem.style[ name ] = old[ name ];
	}

	return ret;
};


var rboxStyle = new RegExp( cssExpand.join( "|" ), "i" );



( function() {

	// Executing both pixelPosition & boxSizingReliable tests require only one layout
	// so they're executed at the same time to save the second computation.
	function computeStyleTests() {

		// This is a singleton, we need to execute it only once
		if ( !div ) {
			return;
		}

		container.style.cssText = "position:absolute;left:-11111px;width:60px;" +
			"margin-top:1px;padding:0;border:0";
		div.style.cssText =
			"position:relative;display:block;box-sizing:border-box;overflow:scroll;" +
			"margin:auto;border:1px;padding:1px;" +
			"width:60%;top:1%";
		documentElement.appendChild( container ).appendChild( div );

		var divStyle = window.getComputedStyle( div );
		pixelPositionVal = divStyle.top !== "1%";

		// Support: Android 4.0 - 4.3 only, Firefox <=3 - 44
		reliableMarginLeftVal = roundPixelMeasures( divStyle.marginLeft ) === 12;

		// Support: Android 4.0 - 4.3 only, Safari <=9.1 - 10.1, iOS <=7.0 - 9.3
		// Some styles come back with percentage values, even though they shouldn't
		div.style.right = "60%";
		pixelBoxStylesVal = roundPixelMeasures( divStyle.right ) === 36;

		// Support: IE 9 - 11 only
		// Detect misreporting of content dimensions for box-sizing:border-box elements
		boxSizingReliableVal = roundPixelMeasures( divStyle.width ) === 36;

		// Support: IE 9 only
		// Detect overflow:scroll screwiness (gh-3699)
		// Support: Chrome <=64
		// Don't get tricked when zoom affects offsetWidth (gh-4029)
		div.style.position = "absolute";
		scrollboxSizeVal = roundPixelMeasures( div.offsetWidth / 3 ) === 12;

		documentElement.removeChild( container );

		// Nullify the div so it wouldn't be stored in the memory and
		// it will also be a sign that checks already performed
		div = null;
	}

	function roundPixelMeasures( measure ) {
		return Math.round( parseFloat( measure ) );
	}

	var pixelPositionVal, boxSizingReliableVal, scrollboxSizeVal, pixelBoxStylesVal,
		reliableTrDimensionsVal, reliableMarginLeftVal,
		container = document.createElement( "div" ),
		div = document.createElement( "div" );

	// Finish early in limited (non-browser) environments
	if ( !div.style ) {
		return;
	}

	// Support: IE <=9 - 11 only
	// Style of cloned element affects source element cloned (#8908)
	div.style.backgroundClip = "content-box";
	div.cloneNode( true ).style.backgroundClip = "";
	support.clearCloneStyle = div.style.backgroundClip === "content-box";

	jQuery.extend( support, {
		boxSizingReliable: function() {
			computeStyleTests();
			return boxSizingReliableVal;
		},
		pixelBoxStyles: function() {
			computeStyleTests();
			return pixelBoxStylesVal;
		},
		pixelPosition: function() {
			computeStyleTests();
			return pixelPositionVal;
		},
		reliableMarginLeft: function() {
			computeStyleTests();
			return reliableMarginLeftVal;
		},
		scrollboxSize: function() {
			computeStyleTests();
			return scrollboxSizeVal;
		},

		// Support: IE 9 - 11+, Edge 15 - 18+
		// IE/Edge misreport `getComputedStyle` of table rows with width/height
		// set in CSS while `offset*` properties report correct values.
		// Behavior in IE 9 is more subtle than in newer versions & it passes
		// some versions of this test; make sure not to make it pass there!
		reliableTrDimensions: function() {
			var table, tr, trChild, trStyle;
			if ( reliableTrDimensionsVal == null ) {
				table = document.createElement( "table" );
				tr = document.createElement( "tr" );
				trChild = document.createElement( "div" );

				table.style.cssText = "position:absolute;left:-11111px";
				tr.style.height = "1px";
				trChild.style.height = "9px";

				documentElement
					.appendChild( table )
					.appendChild( tr )
					.appendChild( trChild );

				trStyle = window.getComputedStyle( tr );
				reliableTrDimensionsVal = parseInt( trStyle.height ) > 3;

				documentElement.removeChild( table );
			}
			return reliableTrDimensionsVal;
		}
	} );
} )();


function curCSS( elem, name, computed ) {
	var width, minWidth, maxWidth, ret,

		// Support: Firefox 51+
		// Retrieving style before computed somehow
		// fixes an issue with getting wrong values
		// on detached elements
		style = elem.style;

	computed = computed || getStyles( elem );

	// getPropertyValue is needed for:
	//   .css('filter') (IE 9 only, #12537)
	//   .css('--customProperty) (#3144)
	if ( computed ) {
		ret = computed.getPropertyValue( name ) || computed[ name ];

		if ( ret === "" && !isAttached( elem ) ) {
			ret = jQuery.style( elem, name );
		}

		// A tribute to the "awesome hack by Dean Edwards"
		// Android Browser returns percentage for some values,
		// but width seems to be reliably pixels.
		// This is against the CSSOM draft spec:
		// https://drafts.csswg.org/cssom/#resolved-values
		if ( !support.pixelBoxStyles() && rnumnonpx.test( ret ) && rboxStyle.test( name ) ) {

			// Remember the original values
			width = style.width;
			minWidth = style.minWidth;
			maxWidth = style.maxWidth;

			// Put in the new values to get a computed value out
			style.minWidth = style.maxWidth = style.width = ret;
			ret = computed.width;

			// Revert the changed values
			style.width = width;
			style.minWidth = minWidth;
			style.maxWidth = maxWidth;
		}
	}

	return ret !== undefined ?

		// Support: IE <=9 - 11 only
		// IE returns zIndex value as an integer.
		ret + "" :
		ret;
}


function addGetHookIf( conditionFn, hookFn ) {

	// Define the hook, we'll check on the first run if it's really needed.
	return {
		get: function() {
			if ( conditionFn() ) {

				// Hook not needed (or it's not possible to use it due
				// to missing dependency), remove it.
				delete this.get;
				return;
			}

			// Hook needed; redefine it so that the support test is not executed again.
			return ( this.get = hookFn ).apply( this, arguments );
		}
	};
}


var cssPrefixes = [ "Webkit", "Moz", "ms" ],
	emptyStyle = document.createElement( "div" ).style,
	vendorProps = {};

// Return a vendor-prefixed property or undefined
function vendorPropName( name ) {

	// Check for vendor prefixed names
	var capName = name[ 0 ].toUpperCase() + name.slice( 1 ),
		i = cssPrefixes.length;

	while ( i-- ) {
		name = cssPrefixes[ i ] + capName;
		if ( name in emptyStyle ) {
			return name;
		}
	}
}

// Return a potentially-mapped jQuery.cssProps or vendor prefixed property
function finalPropName( name ) {
	var final = jQuery.cssProps[ name ] || vendorProps[ name ];

	if ( final ) {
		return final;
	}
	if ( name in emptyStyle ) {
		return name;
	}
	return vendorProps[ name ] = vendorPropName( name ) || name;
}


var

	// Swappable if display is none or starts with table
	// except "table", "table-cell", or "table-caption"
	// See here for display values: https://developer.mozilla.org/en-US/docs/CSS/display
	rdisplayswap = /^(none|table(?!-c[ea]).+)/,
	rcustomProp = /^--/,
	cssShow = { position: "absolute", visibility: "hidden", display: "block" },
	cssNormalTransform = {
		letterSpacing: "0",
		fontWeight: "400"
	};

function setPositiveNumber( _elem, value, subtract ) {

	// Any relative (+/-) values have already been
	// normalized at this point
	var matches = rcssNum.exec( value );
	return matches ?

		// Guard against undefined "subtract", e.g., when used as in cssHooks
		Math.max( 0, matches[ 2 ] - ( subtract || 0 ) ) + ( matches[ 3 ] || "px" ) :
		value;
}

function boxModelAdjustment( elem, dimension, box, isBorderBox, styles, computedVal ) {
	var i = dimension === "width" ? 1 : 0,
		extra = 0,
		delta = 0;

	// Adjustment may not be necessary
	if ( box === ( isBorderBox ? "border" : "content" ) ) {
		return 0;
	}

	for ( ; i < 4; i += 2 ) {

		// Both box models exclude margin
		if ( box === "margin" ) {
			delta += jQuery.css( elem, box + cssExpand[ i ], true, styles );
		}

		// If we get here with a content-box, we're seeking "padding" or "border" or "margin"
		if ( !isBorderBox ) {

			// Add padding
			delta += jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );

			// For "border" or "margin", add border
			if ( box !== "padding" ) {
				delta += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );

			// But still keep track of it otherwise
			} else {
				extra += jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}

		// If we get here with a border-box (content + padding + border), we're seeking "content" or
		// "padding" or "margin"
		} else {

			// For "content", subtract padding
			if ( box === "content" ) {
				delta -= jQuery.css( elem, "padding" + cssExpand[ i ], true, styles );
			}

			// For "content" or "padding", subtract border
			if ( box !== "margin" ) {
				delta -= jQuery.css( elem, "border" + cssExpand[ i ] + "Width", true, styles );
			}
		}
	}

	// Account for positive content-box scroll gutter when requested by providing computedVal
	if ( !isBorderBox && computedVal >= 0 ) {

		// offsetWidth/offsetHeight is a rounded sum of content, padding, scroll gutter, and border
		// Assuming integer scroll gutter, subtract the rest and round down
		delta += Math.max( 0, Math.ceil(
			elem[ "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 ) ] -
			computedVal -
			delta -
			extra -
			0.5

		// If offsetWidth/offsetHeight is unknown, then we can't determine content-box scroll gutter
		// Use an explicit zero to avoid NaN (gh-3964)
		) ) || 0;
	}

	return delta;
}

function getWidthOrHeight( elem, dimension, extra ) {

	// Start with computed style
	var styles = getStyles( elem ),

		// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-4322).
		// Fake content-box until we know it's needed to know the true value.
		boxSizingNeeded = !support.boxSizingReliable() || extra,
		isBorderBox = boxSizingNeeded &&
			jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
		valueIsBorderBox = isBorderBox,

		val = curCSS( elem, dimension, styles ),
		offsetProp = "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 );

	// Support: Firefox <=54
	// Return a confounding non-pixel value or feign ignorance, as appropriate.
	if ( rnumnonpx.test( val ) ) {
		if ( !extra ) {
			return val;
		}
		val = "auto";
	}


	// Support: IE 9 - 11 only
	// Use offsetWidth/offsetHeight for when box sizing is unreliable.
	// In those cases, the computed value can be trusted to be border-box.
	if ( ( !support.boxSizingReliable() && isBorderBox ||

		// Support: IE 10 - 11+, Edge 15 - 18+
		// IE/Edge misreport `getComputedStyle` of table rows with width/height
		// set in CSS while `offset*` properties report correct values.
		// Interestingly, in some cases IE 9 doesn't suffer from this issue.
		!support.reliableTrDimensions() && nodeName( elem, "tr" ) ||

		// Fall back to offsetWidth/offsetHeight when value is "auto"
		// This happens for inline elements with no explicit setting (gh-3571)
		val === "auto" ||

		// Support: Android <=4.1 - 4.3 only
		// Also use offsetWidth/offsetHeight for misreported inline dimensions (gh-3602)
		!parseFloat( val ) && jQuery.css( elem, "display", false, styles ) === "inline" ) &&

		// Make sure the element is visible & connected
		elem.getClientRects().length ) {

		isBorderBox = jQuery.css( elem, "boxSizing", false, styles ) === "border-box";

		// Where available, offsetWidth/offsetHeight approximate border box dimensions.
		// Where not available (e.g., SVG), assume unreliable box-sizing and interpret the
		// retrieved value as a content box dimension.
		valueIsBorderBox = offsetProp in elem;
		if ( valueIsBorderBox ) {
			val = elem[ offsetProp ];
		}
	}

	// Normalize "" and auto
	val = parseFloat( val ) || 0;

	// Adjust for the element's box model
	return ( val +
		boxModelAdjustment(
			elem,
			dimension,
			extra || ( isBorderBox ? "border" : "content" ),
			valueIsBorderBox,
			styles,

			// Provide the current computed size to request scroll gutter calculation (gh-3589)
			val
		)
	) + "px";
}

jQuery.extend( {

	// Add in style property hooks for overriding the default
	// behavior of getting and setting a style property
	cssHooks: {
		opacity: {
			get: function( elem, computed ) {
				if ( computed ) {

					// We should always get a number back from opacity
					var ret = curCSS( elem, "opacity" );
					return ret === "" ? "1" : ret;
				}
			}
		}
	},

	// Don't automatically add "px" to these possibly-unitless properties
	cssNumber: {
		"animationIterationCount": true,
		"columnCount": true,
		"fillOpacity": true,
		"flexGrow": true,
		"flexShrink": true,
		"fontWeight": true,
		"gridArea": true,
		"gridColumn": true,
		"gridColumnEnd": true,
		"gridColumnStart": true,
		"gridRow": true,
		"gridRowEnd": true,
		"gridRowStart": true,
		"lineHeight": true,
		"opacity": true,
		"order": true,
		"orphans": true,
		"widows": true,
		"zIndex": true,
		"zoom": true
	},

	// Add in properties whose names you wish to fix before
	// setting or getting the value
	cssProps: {},

	// Get and set the style property on a DOM Node
	style: function( elem, name, value, extra ) {

		// Don't set styles on text and comment nodes
		if ( !elem || elem.nodeType === 3 || elem.nodeType === 8 || !elem.style ) {
			return;
		}

		// Make sure that we're working with the right name
		var ret, type, hooks,
			origName = camelCase( name ),
			isCustomProp = rcustomProp.test( name ),
			style = elem.style;

		// Make sure that we're working with the right name. We don't
		// want to query the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Gets hook for the prefixed version, then unprefixed version
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// Check if we're setting a value
		if ( value !== undefined ) {
			type = typeof value;

			// Convert "+=" or "-=" to relative numbers (#7345)
			if ( type === "string" && ( ret = rcssNum.exec( value ) ) && ret[ 1 ] ) {
				value = adjustCSS( elem, name, ret );

				// Fixes bug #9237
				type = "number";
			}

			// Make sure that null and NaN values aren't set (#7116)
			if ( value == null || value !== value ) {
				return;
			}

			// If a number was passed in, add the unit (except for certain CSS properties)
			// The isCustomProp check can be removed in jQuery 4.0 when we only auto-append
			// "px" to a few hardcoded values.
			if ( type === "number" && !isCustomProp ) {
				value += ret && ret[ 3 ] || ( jQuery.cssNumber[ origName ] ? "" : "px" );
			}

			// background-* props affect original clone's values
			if ( !support.clearCloneStyle && value === "" && name.indexOf( "background" ) === 0 ) {
				style[ name ] = "inherit";
			}

			// If a hook was provided, use that value, otherwise just set the specified value
			if ( !hooks || !( "set" in hooks ) ||
				( value = hooks.set( elem, value, extra ) ) !== undefined ) {

				if ( isCustomProp ) {
					style.setProperty( name, value );
				} else {
					style[ name ] = value;
				}
			}

		} else {

			// If a hook was provided get the non-computed value from there
			if ( hooks && "get" in hooks &&
				( ret = hooks.get( elem, false, extra ) ) !== undefined ) {

				return ret;
			}

			// Otherwise just get the value from the style object
			return style[ name ];
		}
	},

	css: function( elem, name, extra, styles ) {
		var val, num, hooks,
			origName = camelCase( name ),
			isCustomProp = rcustomProp.test( name );

		// Make sure that we're working with the right name. We don't
		// want to modify the value if it is a CSS custom property
		// since they are user-defined.
		if ( !isCustomProp ) {
			name = finalPropName( origName );
		}

		// Try prefixed name followed by the unprefixed name
		hooks = jQuery.cssHooks[ name ] || jQuery.cssHooks[ origName ];

		// If a hook was provided get the computed value from there
		if ( hooks && "get" in hooks ) {
			val = hooks.get( elem, true, extra );
		}

		// Otherwise, if a way to get the computed value exists, use that
		if ( val === undefined ) {
			val = curCSS( elem, name, styles );
		}

		// Convert "normal" to computed value
		if ( val === "normal" && name in cssNormalTransform ) {
			val = cssNormalTransform[ name ];
		}

		// Make numeric if forced or a qualifier was provided and val looks numeric
		if ( extra === "" || extra ) {
			num = parseFloat( val );
			return extra === true || isFinite( num ) ? num || 0 : val;
		}

		return val;
	}
} );

jQuery.each( [ "height", "width" ], function( _i, dimension ) {
	jQuery.cssHooks[ dimension ] = {
		get: function( elem, computed, extra ) {
			if ( computed ) {

				// Certain elements can have dimension info if we invisibly show them
				// but it must have a current display style that would benefit
				return rdisplayswap.test( jQuery.css( elem, "display" ) ) &&

					// Support: Safari 8+
					// Table columns in Safari have non-zero offsetWidth & zero
					// getBoundingClientRect().width unless display is changed.
					// Support: IE <=11 only
					// Running getBoundingClientRect on a disconnected node
					// in IE throws an error.
					( !elem.getClientRects().length || !elem.getBoundingClientRect().width ) ?
						swap( elem, cssShow, function() {
							return getWidthOrHeight( elem, dimension, extra );
						} ) :
						getWidthOrHeight( elem, dimension, extra );
			}
		},

		set: function( elem, value, extra ) {
			var matches,
				styles = getStyles( elem ),

				// Only read styles.position if the test has a chance to fail
				// to avoid forcing a reflow.
				scrollboxSizeBuggy = !support.scrollboxSize() &&
					styles.position === "absolute",

				// To avoid forcing a reflow, only fetch boxSizing if we need it (gh-3991)
				boxSizingNeeded = scrollboxSizeBuggy || extra,
				isBorderBox = boxSizingNeeded &&
					jQuery.css( elem, "boxSizing", false, styles ) === "border-box",
				subtract = extra ?
					boxModelAdjustment(
						elem,
						dimension,
						extra,
						isBorderBox,
						styles
					) :
					0;

			// Account for unreliable border-box dimensions by comparing offset* to computed and
			// faking a content-box to get border and padding (gh-3699)
			if ( isBorderBox && scrollboxSizeBuggy ) {
				subtract -= Math.ceil(
					elem[ "offset" + dimension[ 0 ].toUpperCase() + dimension.slice( 1 ) ] -
					parseFloat( styles[ dimension ] ) -
					boxModelAdjustment( elem, dimension, "border", false, styles ) -
					0.5
				);
			}

			// Convert to pixels if value adjustment is needed
			if ( subtract && ( matches = rcssNum.exec( value ) ) &&
				( matches[ 3 ] || "px" ) !== "px" ) {

				elem.style[ dimension ] = value;
				value = jQuery.css( elem, dimension );
			}

			return setPositiveNumber( elem, value, subtract );
		}
	};
} );

jQuery.cssHooks.marginLeft = addGetHookIf( support.reliableMarginLeft,
	function( elem, computed ) {
		if ( computed ) {
			return ( parseFloat( curCSS( elem, "marginLeft" ) ) ||
				elem.getBoundingClientRect().left -
					swap( elem, { marginLeft: 0 }, function() {
						return elem.getBoundingClientRect().left;
					} )
				) + "px";
		}
	}
);

// These hooks are used by animate to expand properties
jQuery.each( {
	margin: "",
	padding: "",
	border: "Width"
}, function( prefix, suffix ) {
	jQuery.cssHooks[ prefix + suffix ] = {
		expand: function( value ) {
			var i = 0,
				expanded = {},

				// Assumes a single number if not a string
				parts = typeof value === "string" ? value.split( " " ) : [ value ];

			for ( ; i < 4; i++ ) {
				expanded[ prefix + cssExpand[ i ] + suffix ] =
					parts[ i ] || parts[ i - 2 ] || parts[ 0 ];
			}

			return expanded;
		}
	};

	if ( prefix !== "margin" ) {
		jQuery.cssHooks[ prefix + suffix ].set = setPositiveNumber;
	}
} );

jQuery.fn.extend( {
	css: function( name, value ) {
		return access( this, function( elem, name, value ) {
			var styles, len,
				map = {},
				i = 0;

			if ( Array.isArray( name ) ) {
				styles = getStyles( elem );
				len = name.length;

				for ( ; i < len; i++ ) {
					map[ name[ i ] ] = jQuery.css( elem, name[ i ], false, styles );
				}

				return map;
			}

			return value !== undefined ?
				jQuery.style( elem, name, value ) :
				jQuery.css( elem, name );
		}, name, value, arguments.length > 1 );
	}
} );


function Tween( elem, options, prop, end, easing ) {
	return new Tween.prototype.init( elem, options, prop, end, easing );
}
jQuery.Tween = Tween;

Tween.prototype = {
	constructor: Tween,
	init: function( elem, options, prop, end, easing, unit ) {
		this.elem = elem;
		this.prop = prop;
		this.easing = easing || jQuery.easing._default;
		this.options = options;
		this.start = this.now = this.cur();
		this.end = end;
		this.unit = unit || ( jQuery.cssNumber[ prop ] ? "" : "px" );
	},
	cur: function() {
		var hooks = Tween.propHooks[ this.prop ];

		return hooks && hooks.get ?
			hooks.get( this ) :
			Tween.propHooks._default.get( this );
	},
	run: function( percent ) {
		var eased,
			hooks = Tween.propHooks[ this.prop ];

		if ( this.options.duration ) {
			this.pos = eased = jQuery.easing[ this.easing ](
				percent, this.options.duration * percent, 0, 1, this.options.duration
			);
		} else {
			this.pos = eased = percent;
		}
		this.now = ( this.end - this.start ) * eased + this.start;

		if ( this.options.step ) {
			this.options.step.call( this.elem, this.now, this );
		}

		if ( hooks && hooks.set ) {
			hooks.set( this );
		} else {
			Tween.propHooks._default.set( this );
		}
		return this;
	}
};

Tween.prototype.init.prototype = Tween.prototype;

Tween.propHooks = {
	_default: {
		get: function( tween ) {
			var result;

			// Use a property on the element directly when it is not a DOM element,
			// or when there is no matching style property that exists.
			if ( tween.elem.nodeType !== 1 ||
				tween.elem[ tween.prop ] != null && tween.elem.style[ tween.prop ] == null ) {
				return tween.elem[ tween.prop ];
			}

			// Passing an empty string as a 3rd parameter to .css will automatically
			// attempt a parseFloat and fallback to a string if the parse fails.
			// Simple values such as "10px" are parsed to Float;
			// complex values such as "rotate(1rad)" are returned as-is.
			result = jQuery.css( tween.elem, tween.prop, "" );

			// Empty strings, null, undefined and "auto" are converted to 0.
			return !result || result === "auto" ? 0 : result;
		},
		set: function( tween ) {

			// Use step hook for back compat.
			// Use cssHook if its there.
			// Use .style if available and use plain properties where available.
			if ( jQuery.fx.step[ tween.prop ] ) {
				jQuery.fx.step[ tween.prop ]( tween );
			} else if ( tween.elem.nodeType === 1 && (
					jQuery.cssHooks[ tween.prop ] ||
					tween.elem.style[ finalPropName( tween.prop ) ] != null ) ) {
				jQuery.style( tween.elem, tween.prop, tween.now + tween.unit );
			} else {
				tween.elem[ tween.prop ] = tween.now;
			}
		}
	}
};

// Support: IE <=9 only
// Panic based approach to setting things on disconnected nodes
Tween.propHooks.scrollTop = Tween.propHooks.scrollLeft = {
	set: function( tween ) {
		if ( tween.elem.nodeType && tween.elem.parentNode ) {
			tween.elem[ tween.prop ] = tween.now;
		}
	}
};

jQuery.easing = {
	linear: function( p ) {
		return p;
	},
	swing: function( p ) {
		return 0.5 - Math.cos( p * Math.PI ) / 2;
	},
	_default: "swing"
};

jQuery.fx = Tween.prototype.init;

// Back compat <1.8 extension point
jQuery.fx.step = {};




var
	fxNow, inProgress,
	rfxtypes = /^(?:toggle|show|hide)$/,
	rrun = /queueHooks$/;

function schedule() {
	if ( inProgress ) {
		if ( document.hidden === false && window.requestAnimationFrame ) {
			window.requestAnimationFrame( schedule );
		} else {
			window.setTimeout( schedule, jQuery.fx.interval );
		}

		jQuery.fx.tick();
	}
}

// Animations created synchronously will run synchronously
function createFxNow() {
	window.setTimeout( function() {
		fxNow = undefined;
	} );
	return ( fxNow = Date.now() );
}

// Generate parameters to create a standard animation
function genFx( type, includeWidth ) {
	var which,
		i = 0,
		attrs = { height: type };

	// If we include width, step value is 1 to do all cssExpand values,
	// otherwise step value is 2 to skip over Left and Right
	includeWidth = includeWidth ? 1 : 0;
	for ( ; i < 4; i += 2 - includeWidth ) {
		which = cssExpand[ i ];
		attrs[ "margin" + which ] = attrs[ "padding" + which ] = type;
	}

	if ( includeWidth ) {
		attrs.opacity = attrs.width = type;
	}

	return attrs;
}

function createTween( value, prop, animation ) {
	var tween,
		collection = ( Animation.tweeners[ prop ] || [] ).concat( Animation.tweeners[ "*" ] ),
		index = 0,
		length = collection.length;
	for ( ; index < length; index++ ) {
		if ( ( tween = collection[ index ].call( animation, prop, value ) ) ) {

			// We're done with this property
			return tween;
		}
	}
}

function defaultPrefilter( elem, props, opts ) {
	var prop, value, toggle, hooks, oldfire, propTween, restoreDisplay, display,
		isBox = "width" in props || "height" in props,
		anim = this,
		orig = {},
		style = elem.style,
		hidden = elem.nodeType && isHiddenWithinTree( elem ),
		dataShow = dataPriv.get( elem, "fxshow" );

	// Queue-skipping animations hijack the fx hooks
	if ( !opts.queue ) {
		hooks = jQuery._queueHooks( elem, "fx" );
		if ( hooks.unqueued == null ) {
			hooks.unqueued = 0;
			oldfire = hooks.empty.fire;
			hooks.empty.fire = function() {
				if ( !hooks.unqueued ) {
					oldfire();
				}
			};
		}
		hooks.unqueued++;

		anim.always( function() {

			// Ensure the complete handler is called before this completes
			anim.always( function() {
				hooks.unqueued--;
				if ( !jQuery.queue( elem, "fx" ).length ) {
					hooks.empty.fire();
				}
			} );
		} );
	}

	// Detect show/hide animations
	for ( prop in props ) {
		value = props[ prop ];
		if ( rfxtypes.test( value ) ) {
			delete props[ prop ];
			toggle = toggle || value === "toggle";
			if ( value === ( hidden ? "hide" : "show" ) ) {

				// Pretend to be hidden if this is a "show" and
				// there is still data from a stopped show/hide
				if ( value === "show" && dataShow && dataShow[ prop ] !== undefined ) {
					hidden = true;

				// Ignore all other no-op show/hide data
				} else {
					continue;
				}
			}
			orig[ prop ] = dataShow && dataShow[ prop ] || jQuery.style( elem, prop );
		}
	}

	// Bail out if this is a no-op like .hide().hide()
	propTween = !jQuery.isEmptyObject( props );
	if ( !propTween && jQuery.isEmptyObject( orig ) ) {
		return;
	}

	// Restrict "overflow" and "display" styles during box animations
	if ( isBox && elem.nodeType === 1 ) {

		// Support: IE <=9 - 11, Edge 12 - 15
		// Record all 3 overflow attributes because IE does not infer the shorthand
		// from identically-valued overflowX and overflowY and Edge just mirrors
		// the overflowX value there.
		opts.overflow = [ style.overflow, style.overflowX, style.overflowY ];

		// Identify a display type, preferring old show/hide data over the CSS cascade
		restoreDisplay = dataShow && dataShow.display;
		if ( restoreDisplay == null ) {
			restoreDisplay = dataPriv.get( elem, "display" );
		}
		display = jQuery.css( elem, "display" );
		if ( display === "none" ) {
			if ( restoreDisplay ) {
				display = restoreDisplay;
			} else {

				// Get nonempty value(s) by temporarily forcing visibility
				showHide( [ elem ], true );
				restoreDisplay = elem.style.display || restoreDisplay;
				display = jQuery.css( elem, "display" );
				showHide( [ elem ] );
			}
		}

		// Animate inline elements as inline-block
		if ( display === "inline" || display === "inline-block" && restoreDisplay != null ) {
			if ( jQuery.css( elem, "float" ) === "none" ) {

				// Restore the original display value at the end of pure show/hide animations
				if ( !propTween ) {
					anim.done( function() {
						style.display = restoreDisplay;
					} );
					if ( restoreDisplay == null ) {
						display = style.display;
						restoreDisplay = display === "none" ? "" : display;
					}
				}
				style.display = "inline-block";
			}
		}
	}

	if ( opts.overflow ) {
		style.overflow = "hidden";
		anim.always( function() {
			style.overflow = opts.overflow[ 0 ];
			style.overflowX = opts.overflow[ 1 ];
			style.overflowY = opts.overflow[ 2 ];
		} );
	}

	// Implement show/hide animations
	propTween = false;
	for ( prop in orig ) {

		// General show/hide setup for this element animation
		if ( !propTween ) {
			if ( dataShow ) {
				if ( "hidden" in dataShow ) {
					hidden = dataShow.hidden;
				}
			} else {
				dataShow = dataPriv.access( elem, "fxshow", { display: restoreDisplay } );
			}

			// Store hidden/visible for toggle so `.stop().toggle()` "reverses"
			if ( toggle ) {
				dataShow.hidden = !hidden;
			}

			// Show elements before animating them
			if ( hidden ) {
				showHide( [ elem ], true );
			}

			/* eslint-disable no-loop-func */

			anim.done( function() {

			/* eslint-enable no-loop-func */

				// The final step of a "hide" animation is actually hiding the element
				if ( !hidden ) {
					showHide( [ elem ] );
				}
				dataPriv.remove( elem, "fxshow" );
				for ( prop in orig ) {
					jQuery.style( elem, prop, orig[ prop ] );
				}
			} );
		}

		// Per-property setup
		propTween = createTween( hidden ? dataShow[ prop ] : 0, prop, anim );
		if ( !( prop in dataShow ) ) {
			dataShow[ prop ] = propTween.start;
			if ( hidden ) {
				propTween.end = propTween.start;
				propTween.start = 0;
			}
		}
	}
}

function propFilter( props, specialEasing ) {
	var index, name, easing, value, hooks;

	// camelCase, specialEasing and expand cssHook pass
	for ( index in props ) {
		name = camelCase( index );
		easing = specialEasing[ name ];
		value = props[ index ];
		if ( Array.isArray( value ) ) {
			easing = value[ 1 ];
			value = props[ index ] = value[ 0 ];
		}

		if ( index !== name ) {
			props[ name ] = value;
			delete props[ index ];
		}

		hooks = jQuery.cssHooks[ name ];
		if ( hooks && "expand" in hooks ) {
			value = hooks.expand( value );
			delete props[ name ];

			// Not quite $.extend, this won't overwrite existing keys.
			// Reusing 'index' because we have the correct "name"
			for ( index in value ) {
				if ( !( index in props ) ) {
					props[ index ] = value[ index ];
					specialEasing[ index ] = easing;
				}
			}
		} else {
			specialEasing[ name ] = easing;
		}
	}
}

function Animation( elem, properties, options ) {
	var result,
		stopped,
		index = 0,
		length = Animation.prefilters.length,
		deferred = jQuery.Deferred().always( function() {

			// Don't match elem in the :animated selector
			delete tick.elem;
		} ),
		tick = function() {
			if ( stopped ) {
				return false;
			}
			var currentTime = fxNow || createFxNow(),
				remaining = Math.max( 0, animation.startTime + animation.duration - currentTime ),

				// Support: Android 2.3 only
				// Archaic crash bug won't allow us to use `1 - ( 0.5 || 0 )` (#12497)
				temp = remaining / animation.duration || 0,
				percent = 1 - temp,
				index = 0,
				length = animation.tweens.length;

			for ( ; index < length; index++ ) {
				animation.tweens[ index ].run( percent );
			}

			deferred.notifyWith( elem, [ animation, percent, remaining ] );

			// If there's more to do, yield
			if ( percent < 1 && length ) {
				return remaining;
			}

			// If this was an empty animation, synthesize a final progress notification
			if ( !length ) {
				deferred.notifyWith( elem, [ animation, 1, 0 ] );
			}

			// Resolve the animation and report its conclusion
			deferred.resolveWith( elem, [ animation ] );
			return false;
		},
		animation = deferred.promise( {
			elem: elem,
			props: jQuery.extend( {}, properties ),
			opts: jQuery.extend( true, {
				specialEasing: {},
				easing: jQuery.easing._default
			}, options ),
			originalProperties: properties,
			originalOptions: options,
			startTime: fxNow || createFxNow(),
			duration: options.duration,
			tweens: [],
			createTween: function( prop, end ) {
				var tween = jQuery.Tween( elem, animation.opts, prop, end,
						animation.opts.specialEasing[ prop ] || animation.opts.easing );
				animation.tweens.push( tween );
				return tween;
			},
			stop: function( gotoEnd ) {
				var index = 0,

					// If we are going to the end, we want to run all the tweens
					// otherwise we skip this part
					length = gotoEnd ? animation.tweens.length : 0;
				if ( stopped ) {
					return this;
				}
				stopped = true;
				for ( ; index < length; index++ ) {
					animation.tweens[ index ].run( 1 );
				}

				// Resolve when we played the last frame; otherwise, reject
				if ( gotoEnd ) {
					deferred.notifyWith( elem, [ animation, 1, 0 ] );
					deferred.resolveWith( elem, [ animation, gotoEnd ] );
				} else {
					deferred.rejectWith( elem, [ animation, gotoEnd ] );
				}
				return this;
			}
		} ),
		props = animation.props;

	propFilter( props, animation.opts.specialEasing );

	for ( ; index < length; index++ ) {
		result = Animation.prefilters[ index ].call( animation, elem, props, animation.opts );
		if ( result ) {
			if ( isFunction( result.stop ) ) {
				jQuery._queueHooks( animation.elem, animation.opts.queue ).stop =
					result.stop.bind( result );
			}
			return result;
		}
	}

	jQuery.map( props, createTween, animation );

	if ( isFunction( animation.opts.start ) ) {
		animation.opts.start.call( elem, animation );
	}

	// Attach callbacks from options
	animation
		.progress( animation.opts.progress )
		.done( animation.opts.done, animation.opts.complete )
		.fail( animation.opts.fail )
		.always( animation.opts.always );

	jQuery.fx.timer(
		jQuery.extend( tick, {
			elem: elem,
			anim: animation,
			queue: animation.opts.queue
		} )
	);

	return animation;
}

jQuery.Animation = jQuery.extend( Animation, {

	tweeners: {
		"*": [ function( prop, value ) {
			var tween = this.createTween( prop, value );
			adjustCSS( tween.elem, prop, rcssNum.exec( value ), tween );
			return tween;
		} ]
	},

	tweener: function( props, callback ) {
		if ( isFunction( props ) ) {
			callback = props;
			props = [ "*" ];
		} else {
			props = props.match( rnothtmlwhite );
		}

		var prop,
			index = 0,
			length = props.length;

		for ( ; index < length; index++ ) {
			prop = props[ index ];
			Animation.tweeners[ prop ] = Animation.tweeners[ prop ] || [];
			Animation.tweeners[ prop ].unshift( callback );
		}
	},

	prefilters: [ defaultPrefilter ],

	prefilter: function( callback, prepend ) {
		if ( prepend ) {
			Animation.prefilters.unshift( callback );
		} else {
			Animation.prefilters.push( callback );
		}
	}
} );

jQuery.speed = function( speed, easing, fn ) {
	var opt = speed && typeof speed === "object" ? jQuery.extend( {}, speed ) : {
		complete: fn || !fn && easing ||
			isFunction( speed ) && speed,
		duration: speed,
		easing: fn && easing || easing && !isFunction( easing ) && easing
	};

	// Go to the end state if fx are off
	if ( jQuery.fx.off ) {
		opt.duration = 0;

	} else {
		if ( typeof opt.duration !== "number" ) {
			if ( opt.duration in jQuery.fx.speeds ) {
				opt.duration = jQuery.fx.speeds[ opt.duration ];

			} else {
				opt.duration = jQuery.fx.speeds._default;
			}
		}
	}

	// Normalize opt.queue - true/undefined/null -> "fx"
	if ( opt.queue == null || opt.queue === true ) {
		opt.queue = "fx";
	}

	// Queueing
	opt.old = opt.complete;

	opt.complete = function() {
		if ( isFunction( opt.old ) ) {
			opt.old.call( this );
		}

		if ( opt.queue ) {
			jQuery.dequeue( this, opt.queue );
		}
	};

	return opt;
};

jQuery.fn.extend( {
	fadeTo: function( speed, to, easing, callback ) {

		// Show any hidden elements after setting opacity to 0
		return this.filter( isHiddenWithinTree ).css( "opacity", 0 ).show()

			// Animate to the value specified
			.end().animate( { opacity: to }, speed, easing, callback );
	},
	animate: function( prop, speed, easing, callback ) {
		var empty = jQuery.isEmptyObject( prop ),
			optall = jQuery.speed( speed, easing, callback ),
			doAnimation = function() {

				// Operate on a copy of prop so per-property easing won't be lost
				var anim = Animation( this, jQuery.extend( {}, prop ), optall );

				// Empty animations, or finishing resolves immediately
				if ( empty || dataPriv.get( this, "finish" ) ) {
					anim.stop( true );
				}
			};
			doAnimation.finish = doAnimation;

		return empty || optall.queue === false ?
			this.each( doAnimation ) :
			this.queue( optall.queue, doAnimation );
	},
	stop: function( type, clearQueue, gotoEnd ) {
		var stopQueue = function( hooks ) {
			var stop = hooks.stop;
			delete hooks.stop;
			stop( gotoEnd );
		};

		if ( typeof type !== "string" ) {
			gotoEnd = clearQueue;
			clearQueue = type;
			type = undefined;
		}
		if ( clearQueue ) {
			this.queue( type || "fx", [] );
		}

		return this.each( function() {
			var dequeue = true,
				index = type != null && type + "queueHooks",
				timers = jQuery.timers,
				data = dataPriv.get( this );

			if ( index ) {
				if ( data[ index ] && data[ index ].stop ) {
					stopQueue( data[ index ] );
				}
			} else {
				for ( index in data ) {
					if ( data[ index ] && data[ index ].stop && rrun.test( index ) ) {
						stopQueue( data[ index ] );
					}
				}
			}

			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this &&
					( type == null || timers[ index ].queue === type ) ) {

					timers[ index ].anim.stop( gotoEnd );
					dequeue = false;
					timers.splice( index, 1 );
				}
			}

			// Start the next in the queue if the last step wasn't forced.
			// Timers currently will call their complete callbacks, which
			// will dequeue but only if they were gotoEnd.
			if ( dequeue || !gotoEnd ) {
				jQuery.dequeue( this, type );
			}
		} );
	},
	finish: function( type ) {
		if ( type !== false ) {
			type = type || "fx";
		}
		return this.each( function() {
			var index,
				data = dataPriv.get( this ),
				queue = data[ type + "queue" ],
				hooks = data[ type + "queueHooks" ],
				timers = jQuery.timers,
				length = queue ? queue.length : 0;

			// Enable finishing flag on private data
			data.finish = true;

			// Empty the queue first
			jQuery.queue( this, type, [] );

			if ( hooks && hooks.stop ) {
				hooks.stop.call( this, true );
			}

			// Look for any active animations, and finish them
			for ( index = timers.length; index--; ) {
				if ( timers[ index ].elem === this && timers[ index ].queue === type ) {
					timers[ index ].anim.stop( true );
					timers.splice( index, 1 );
				}
			}

			// Look for any animations in the old queue and finish them
			for ( index = 0; index < length; index++ ) {
				if ( queue[ index ] && queue[ index ].finish ) {
					queue[ index ].finish.call( this );
				}
			}

			// Turn off finishing flag
			delete data.finish;
		} );
	}
} );

jQuery.each( [ "toggle", "show", "hide" ], function( _i, name ) {
	var cssFn = jQuery.fn[ name ];
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return speed == null || typeof speed === "boolean" ?
			cssFn.apply( this, arguments ) :
			this.animate( genFx( name, true ), speed, easing, callback );
	};
} );

// Generate shortcuts for custom animations
jQuery.each( {
	slideDown: genFx( "show" ),
	slideUp: genFx( "hide" ),
	slideToggle: genFx( "toggle" ),
	fadeIn: { opacity: "show" },
	fadeOut: { opacity: "hide" },
	fadeToggle: { opacity: "toggle" }
}, function( name, props ) {
	jQuery.fn[ name ] = function( speed, easing, callback ) {
		return this.animate( props, speed, easing, callback );
	};
} );

jQuery.timers = [];
jQuery.fx.tick = function() {
	var timer,
		i = 0,
		timers = jQuery.timers;

	fxNow = Date.now();

	for ( ; i < timers.length; i++ ) {
		timer = timers[ i ];

		// Run the timer and safely remove it when done (allowing for external removal)
		if ( !timer() && timers[ i ] === timer ) {
			timers.splice( i--, 1 );
		}
	}

	if ( !timers.length ) {
		jQuery.fx.stop();
	}
	fxNow = undefined;
};

jQuery.fx.timer = function( timer ) {
	jQuery.timers.push( timer );
	jQuery.fx.start();
};

jQuery.fx.interval = 13;
jQuery.fx.start = function() {
	if ( inProgress ) {
		return;
	}

	inProgress = true;
	schedule();
};

jQuery.fx.stop = function() {
	inProgress = null;
};

jQuery.fx.speeds = {
	slow: 600,
	fast: 200,

	// Default speed
	_default: 400
};


// Based off of the plugin by Clint Helfers, with permission.
// https://web.archive.org/web/20100324014747/http://blindsignals.com/index.php/2009/07/jquery-delay/
jQuery.fn.delay = function( time, type ) {
	time = jQuery.fx ? jQuery.fx.speeds[ time ] || time : time;
	type = type || "fx";

	return this.queue( type, function( next, hooks ) {
		var timeout = window.setTimeout( next, time );
		hooks.stop = function() {
			window.clearTimeout( timeout );
		};
	} );
};


( function() {
	var input = document.createElement( "input" ),
		select = document.createElement( "select" ),
		opt = select.appendChild( document.createElement( "option" ) );

	input.type = "checkbox";

	// Support: Android <=4.3 only
	// Default value for a checkbox should be "on"
	support.checkOn = input.value !== "";

	// Support: IE <=11 only
	// Must access selectedIndex to make default options select
	support.optSelected = opt.selected;

	// Support: IE <=11 only
	// An input loses its value after becoming a radio
	input = document.createElement( "input" );
	input.value = "t";
	input.type = "radio";
	support.radioValue = input.value === "t";
} )();


var boolHook,
	attrHandle = jQuery.expr.attrHandle;

jQuery.fn.extend( {
	attr: function( name, value ) {
		return access( this, jQuery.attr, name, value, arguments.length > 1 );
	},

	removeAttr: function( name ) {
		return this.each( function() {
			jQuery.removeAttr( this, name );
		} );
	}
} );

jQuery.extend( {
	attr: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set attributes on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		// Fallback to prop when attributes are not supported
		if ( typeof elem.getAttribute === "undefined" ) {
			return jQuery.prop( elem, name, value );
		}

		// Attribute hooks are determined by the lowercase version
		// Grab necessary hook if one is defined
		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {
			hooks = jQuery.attrHooks[ name.toLowerCase() ] ||
				( jQuery.expr.match.bool.test( name ) ? boolHook : undefined );
		}

		if ( value !== undefined ) {
			if ( value === null ) {
				jQuery.removeAttr( elem, name );
				return;
			}

			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			elem.setAttribute( name, value + "" );
			return value;
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		ret = jQuery.find.attr( elem, name );

		// Non-existent attributes return null, we normalize to undefined
		return ret == null ? undefined : ret;
	},

	attrHooks: {
		type: {
			set: function( elem, value ) {
				if ( !support.radioValue && value === "radio" &&
					nodeName( elem, "input" ) ) {
					var val = elem.value;
					elem.setAttribute( "type", value );
					if ( val ) {
						elem.value = val;
					}
					return value;
				}
			}
		}
	},

	removeAttr: function( elem, value ) {
		var name,
			i = 0,

			// Attribute names can contain non-HTML whitespace characters
			// https://html.spec.whatwg.org/multipage/syntax.html#attributes-2
			attrNames = value && value.match( rnothtmlwhite );

		if ( attrNames && elem.nodeType === 1 ) {
			while ( ( name = attrNames[ i++ ] ) ) {
				elem.removeAttribute( name );
			}
		}
	}
} );

// Hooks for boolean attributes
boolHook = {
	set: function( elem, value, name ) {
		if ( value === false ) {

			// Remove boolean attributes when set to false
			jQuery.removeAttr( elem, name );
		} else {
			elem.setAttribute( name, name );
		}
		return name;
	}
};

jQuery.each( jQuery.expr.match.bool.source.match( /\w+/g ), function( _i, name ) {
	var getter = attrHandle[ name ] || jQuery.find.attr;

	attrHandle[ name ] = function( elem, name, isXML ) {
		var ret, handle,
			lowercaseName = name.toLowerCase();

		if ( !isXML ) {

			// Avoid an infinite loop by temporarily removing this function from the getter
			handle = attrHandle[ lowercaseName ];
			attrHandle[ lowercaseName ] = ret;
			ret = getter( elem, name, isXML ) != null ?
				lowercaseName :
				null;
			attrHandle[ lowercaseName ] = handle;
		}
		return ret;
	};
} );




var rfocusable = /^(?:input|select|textarea|button)$/i,
	rclickable = /^(?:a|area)$/i;

jQuery.fn.extend( {
	prop: function( name, value ) {
		return access( this, jQuery.prop, name, value, arguments.length > 1 );
	},

	removeProp: function( name ) {
		return this.each( function() {
			delete this[ jQuery.propFix[ name ] || name ];
		} );
	}
} );

jQuery.extend( {
	prop: function( elem, name, value ) {
		var ret, hooks,
			nType = elem.nodeType;

		// Don't get/set properties on text, comment and attribute nodes
		if ( nType === 3 || nType === 8 || nType === 2 ) {
			return;
		}

		if ( nType !== 1 || !jQuery.isXMLDoc( elem ) ) {

			// Fix name and attach hooks
			name = jQuery.propFix[ name ] || name;
			hooks = jQuery.propHooks[ name ];
		}

		if ( value !== undefined ) {
			if ( hooks && "set" in hooks &&
				( ret = hooks.set( elem, value, name ) ) !== undefined ) {
				return ret;
			}

			return ( elem[ name ] = value );
		}

		if ( hooks && "get" in hooks && ( ret = hooks.get( elem, name ) ) !== null ) {
			return ret;
		}

		return elem[ name ];
	},

	propHooks: {
		tabIndex: {
			get: function( elem ) {

				// Support: IE <=9 - 11 only
				// elem.tabIndex doesn't always return the
				// correct value when it hasn't been explicitly set
				// https://web.archive.org/web/20141116233347/http://fluidproject.org/blog/2008/01/09/getting-setting-and-removing-tabindex-values-with-javascript/
				// Use proper attribute retrieval(#12072)
				var tabindex = jQuery.find.attr( elem, "tabindex" );

				if ( tabindex ) {
					return parseInt( tabindex, 10 );
				}

				if (
					rfocusable.test( elem.nodeName ) ||
					rclickable.test( elem.nodeName ) &&
					elem.href
				) {
					return 0;
				}

				return -1;
			}
		}
	},

	propFix: {
		"for": "htmlFor",
		"class": "className"
	}
} );

// Support: IE <=11 only
// Accessing the selectedIndex property
// forces the browser to respect setting selected
// on the option
// The getter ensures a default option is selected
// when in an optgroup
// eslint rule "no-unused-expressions" is disabled for this code
// since it considers such accessions noop
if ( !support.optSelected ) {
	jQuery.propHooks.selected = {
		get: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent && parent.parentNode ) {
				parent.parentNode.selectedIndex;
			}
			return null;
		},
		set: function( elem ) {

			/* eslint no-unused-expressions: "off" */

			var parent = elem.parentNode;
			if ( parent ) {
				parent.selectedIndex;

				if ( parent.parentNode ) {
					parent.parentNode.selectedIndex;
				}
			}
		}
	};
}

jQuery.each( [
	"tabIndex",
	"readOnly",
	"maxLength",
	"cellSpacing",
	"cellPadding",
	"rowSpan",
	"colSpan",
	"useMap",
	"frameBorder",
	"contentEditable"
], function() {
	jQuery.propFix[ this.toLowerCase() ] = this;
} );




	// Strip and collapse whitespace according to HTML spec
	// https://infra.spec.whatwg.org/#strip-and-collapse-ascii-whitespace
	function stripAndCollapse( value ) {
		var tokens = value.match( rnothtmlwhite ) || [];
		return tokens.join( " " );
	}


function getClass( elem ) {
	return elem.getAttribute && elem.getAttribute( "class" ) || "";
}

function classesToArray( value ) {
	if ( Array.isArray( value ) ) {
		return value;
	}
	if ( typeof value === "string" ) {
		return value.match( rnothtmlwhite ) || [];
	}
	return [];
}

jQuery.fn.extend( {
	addClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).addClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		classes = classesToArray( value );

		if ( classes.length ) {
			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );
				cur = elem.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {
						if ( cur.indexOf( " " + clazz + " " ) < 0 ) {
							cur += clazz + " ";
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						elem.setAttribute( "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	removeClass: function( value ) {
		var classes, elem, cur, curValue, clazz, j, finalValue,
			i = 0;

		if ( isFunction( value ) ) {
			return this.each( function( j ) {
				jQuery( this ).removeClass( value.call( this, j, getClass( this ) ) );
			} );
		}

		if ( !arguments.length ) {
			return this.attr( "class", "" );
		}

		classes = classesToArray( value );

		if ( classes.length ) {
			while ( ( elem = this[ i++ ] ) ) {
				curValue = getClass( elem );

				// This expression is here for better compressibility (see addClass)
				cur = elem.nodeType === 1 && ( " " + stripAndCollapse( curValue ) + " " );

				if ( cur ) {
					j = 0;
					while ( ( clazz = classes[ j++ ] ) ) {

						// Remove *all* instances
						while ( cur.indexOf( " " + clazz + " " ) > -1 ) {
							cur = cur.replace( " " + clazz + " ", " " );
						}
					}

					// Only assign if different to avoid unneeded rendering.
					finalValue = stripAndCollapse( cur );
					if ( curValue !== finalValue ) {
						elem.setAttribute( "class", finalValue );
					}
				}
			}
		}

		return this;
	},

	toggleClass: function( value, stateVal ) {
		var type = typeof value,
			isValidValue = type === "string" || Array.isArray( value );

		if ( typeof stateVal === "boolean" && isValidValue ) {
			return stateVal ? this.addClass( value ) : this.removeClass( value );
		}

		if ( isFunction( value ) ) {
			return this.each( function( i ) {
				jQuery( this ).toggleClass(
					value.call( this, i, getClass( this ), stateVal ),
					stateVal
				);
			} );
		}

		return this.each( function() {
			var className, i, self, classNames;

			if ( isValidValue ) {

				// Toggle individual class names
				i = 0;
				self = jQuery( this );
				classNames = classesToArray( value );

				while ( ( className = classNames[ i++ ] ) ) {

					// Check each className given, space separated list
					if ( self.hasClass( className ) ) {
						self.removeClass( className );
					} else {
						self.addClass( className );
					}
				}

			// Toggle whole class name
			} else if ( value === undefined || type === "boolean" ) {
				className = getClass( this );
				if ( className ) {

					// Store className if set
					dataPriv.set( this, "__className__", className );
				}

				// If the element has a class name or if we're passed `false`,
				// then remove the whole classname (if there was one, the above saved it).
				// Otherwise bring back whatever was previously saved (if anything),
				// falling back to the empty string if nothing was stored.
				if ( this.setAttribute ) {
					this.setAttribute( "class",
						className || value === false ?
						"" :
						dataPriv.get( this, "__className__" ) || ""
					);
				}
			}
		} );
	},

	hasClass: function( selector ) {
		var className, elem,
			i = 0;

		className = " " + selector + " ";
		while ( ( elem = this[ i++ ] ) ) {
			if ( elem.nodeType === 1 &&
				( " " + stripAndCollapse( getClass( elem ) ) + " " ).indexOf( className ) > -1 ) {
					return true;
			}
		}

		return false;
	}
} );




var rreturn = /\r/g;

jQuery.fn.extend( {
	val: function( value ) {
		var hooks, ret, valueIsFunction,
			elem = this[ 0 ];

		if ( !arguments.length ) {
			if ( elem ) {
				hooks = jQuery.valHooks[ elem.type ] ||
					jQuery.valHooks[ elem.nodeName.toLowerCase() ];

				if ( hooks &&
					"get" in hooks &&
					( ret = hooks.get( elem, "value" ) ) !== undefined
				) {
					return ret;
				}

				ret = elem.value;

				// Handle most common string cases
				if ( typeof ret === "string" ) {
					return ret.replace( rreturn, "" );
				}

				// Handle cases where value is null/undef or number
				return ret == null ? "" : ret;
			}

			return;
		}

		valueIsFunction = isFunction( value );

		return this.each( function( i ) {
			var val;

			if ( this.nodeType !== 1 ) {
				return;
			}

			if ( valueIsFunction ) {
				val = value.call( this, i, jQuery( this ).val() );
			} else {
				val = value;
			}

			// Treat null/undefined as ""; convert numbers to string
			if ( val == null ) {
				val = "";

			} else if ( typeof val === "number" ) {
				val += "";

			} else if ( Array.isArray( val ) ) {
				val = jQuery.map( val, function( value ) {
					return value == null ? "" : value + "";
				} );
			}

			hooks = jQuery.valHooks[ this.type ] || jQuery.valHooks[ this.nodeName.toLowerCase() ];

			// If set returns undefined, fall back to normal setting
			if ( !hooks || !( "set" in hooks ) || hooks.set( this, val, "value" ) === undefined ) {
				this.value = val;
			}
		} );
	}
} );

jQuery.extend( {
	valHooks: {
		option: {
			get: function( elem ) {

				var val = jQuery.find.attr( elem, "value" );
				return val != null ?
					val :

					// Support: IE <=10 - 11 only
					// option.text throws exceptions (#14686, #14858)
					// Strip and collapse whitespace
					// https://html.spec.whatwg.org/#strip-and-collapse-whitespace
					stripAndCollapse( jQuery.text( elem ) );
			}
		},
		select: {
			get: function( elem ) {
				var value, option, i,
					options = elem.options,
					index = elem.selectedIndex,
					one = elem.type === "select-one",
					values = one ? null : [],
					max = one ? index + 1 : options.length;

				if ( index < 0 ) {
					i = max;

				} else {
					i = one ? index : 0;
				}

				// Loop through all the selected options
				for ( ; i < max; i++ ) {
					option = options[ i ];

					// Support: IE <=9 only
					// IE8-9 doesn't update selected after form reset (#2551)
					if ( ( option.selected || i === index ) &&

							// Don't return options that are disabled or in a disabled optgroup
							!option.disabled &&
							( !option.parentNode.disabled ||
								!nodeName( option.parentNode, "optgroup" ) ) ) {

						// Get the specific value for the option
						value = jQuery( option ).val();

						// We don't need an array for one selects
						if ( one ) {
							return value;
						}

						// Multi-Selects return an array
						values.push( value );
					}
				}

				return values;
			},

			set: function( elem, value ) {
				var optionSet, option,
					options = elem.options,
					values = jQuery.makeArray( value ),
					i = options.length;

				while ( i-- ) {
					option = options[ i ];

					/* eslint-disable no-cond-assign */

					if ( option.selected =
						jQuery.inArray( jQuery.valHooks.option.get( option ), values ) > -1
					) {
						optionSet = true;
					}

					/* eslint-enable no-cond-assign */
				}

				// Force browsers to behave consistently when non-matching value is set
				if ( !optionSet ) {
					elem.selectedIndex = -1;
				}
				return values;
			}
		}
	}
} );

// Radios and checkboxes getter/setter
jQuery.each( [ "radio", "checkbox" ], function() {
	jQuery.valHooks[ this ] = {
		set: function( elem, value ) {
			if ( Array.isArray( value ) ) {
				return ( elem.checked = jQuery.inArray( jQuery( elem ).val(), value ) > -1 );
			}
		}
	};
	if ( !support.checkOn ) {
		jQuery.valHooks[ this ].get = function( elem ) {
			return elem.getAttribute( "value" ) === null ? "on" : elem.value;
		};
	}
} );




// Return jQuery for attributes-only inclusion


support.focusin = "onfocusin" in window;


var rfocusMorph = /^(?:focusinfocus|focusoutblur)$/,
	stopPropagationCallback = function( e ) {
		e.stopPropagation();
	};

jQuery.extend( jQuery.event, {

	trigger: function( event, data, elem, onlyHandlers ) {

		var i, cur, tmp, bubbleType, ontype, handle, special, lastElement,
			eventPath = [ elem || document ],
			type = hasOwn.call( event, "type" ) ? event.type : event,
			namespaces = hasOwn.call( event, "namespace" ) ? event.namespace.split( "." ) : [];

		cur = lastElement = tmp = elem = elem || document;

		// Don't do events on text and comment nodes
		if ( elem.nodeType === 3 || elem.nodeType === 8 ) {
			return;
		}

		// focus/blur morphs to focusin/out; ensure we're not firing them right now
		if ( rfocusMorph.test( type + jQuery.event.triggered ) ) {
			return;
		}

		if ( type.indexOf( "." ) > -1 ) {

			// Namespaced trigger; create a regexp to match event type in handle()
			namespaces = type.split( "." );
			type = namespaces.shift();
			namespaces.sort();
		}
		ontype = type.indexOf( ":" ) < 0 && "on" + type;

		// Caller can pass in a jQuery.Event object, Object, or just an event type string
		event = event[ jQuery.expando ] ?
			event :
			new jQuery.Event( type, typeof event === "object" && event );

		// Trigger bitmask: & 1 for native handlers; & 2 for jQuery (always true)
		event.isTrigger = onlyHandlers ? 2 : 3;
		event.namespace = namespaces.join( "." );
		event.rnamespace = event.namespace ?
			new RegExp( "(^|\\.)" + namespaces.join( "\\.(?:.*\\.|)" ) + "(\\.|$)" ) :
			null;

		// Clean up the event in case it is being reused
		event.result = undefined;
		if ( !event.target ) {
			event.target = elem;
		}

		// Clone any incoming data and prepend the event, creating the handler arg list
		data = data == null ?
			[ event ] :
			jQuery.makeArray( data, [ event ] );

		// Allow special events to draw outside the lines
		special = jQuery.event.special[ type ] || {};
		if ( !onlyHandlers && special.trigger && special.trigger.apply( elem, data ) === false ) {
			return;
		}

		// Determine event propagation path in advance, per W3C events spec (#9951)
		// Bubble up to document, then to window; watch for a global ownerDocument var (#9724)
		if ( !onlyHandlers && !special.noBubble && !isWindow( elem ) ) {

			bubbleType = special.delegateType || type;
			if ( !rfocusMorph.test( bubbleType + type ) ) {
				cur = cur.parentNode;
			}
			for ( ; cur; cur = cur.parentNode ) {
				eventPath.push( cur );
				tmp = cur;
			}

			// Only add window if we got to document (e.g., not plain obj or detached DOM)
			if ( tmp === ( elem.ownerDocument || document ) ) {
				eventPath.push( tmp.defaultView || tmp.parentWindow || window );
			}
		}

		// Fire handlers on the event path
		i = 0;
		while ( ( cur = eventPath[ i++ ] ) && !event.isPropagationStopped() ) {
			lastElement = cur;
			event.type = i > 1 ?
				bubbleType :
				special.bindType || type;

			// jQuery handler
			handle = (
					dataPriv.get( cur, "events" ) || Object.create( null )
				)[ event.type ] &&
				dataPriv.get( cur, "handle" );
			if ( handle ) {
				handle.apply( cur, data );
			}

			// Native handler
			handle = ontype && cur[ ontype ];
			if ( handle && handle.apply && acceptData( cur ) ) {
				event.result = handle.apply( cur, data );
				if ( event.result === false ) {
					event.preventDefault();
				}
			}
		}
		event.type = type;

		// If nobody prevented the default action, do it now
		if ( !onlyHandlers && !event.isDefaultPrevented() ) {

			if ( ( !special._default ||
				special._default.apply( eventPath.pop(), data ) === false ) &&
				acceptData( elem ) ) {

				// Call a native DOM method on the target with the same name as the event.
				// Don't do default actions on window, that's where global variables be (#6170)
				if ( ontype && isFunction( elem[ type ] ) && !isWindow( elem ) ) {

					// Don't re-trigger an onFOO event when we call its FOO() method
					tmp = elem[ ontype ];

					if ( tmp ) {
						elem[ ontype ] = null;
					}

					// Prevent re-triggering of the same event, since we already bubbled it above
					jQuery.event.triggered = type;

					if ( event.isPropagationStopped() ) {
						lastElement.addEventListener( type, stopPropagationCallback );
					}

					elem[ type ]();

					if ( event.isPropagationStopped() ) {
						lastElement.removeEventListener( type, stopPropagationCallback );
					}

					jQuery.event.triggered = undefined;

					if ( tmp ) {
						elem[ ontype ] = tmp;
					}
				}
			}
		}

		return event.result;
	},

	// Piggyback on a donor event to simulate a different one
	// Used only for `focus(in | out)` events
	simulate: function( type, elem, event ) {
		var e = jQuery.extend(
			new jQuery.Event(),
			event,
			{
				type: type,
				isSimulated: true
			}
		);

		jQuery.event.trigger( e, null, elem );
	}

} );

jQuery.fn.extend( {

	trigger: function( type, data ) {
		return this.each( function() {
			jQuery.event.trigger( type, data, this );
		} );
	},
	triggerHandler: function( type, data ) {
		var elem = this[ 0 ];
		if ( elem ) {
			return jQuery.event.trigger( type, data, elem, true );
		}
	}
} );


// Support: Firefox <=44
// Firefox doesn't have focus(in | out) events
// Related ticket - https://bugzilla.mozilla.org/show_bug.cgi?id=687787
//
// Support: Chrome <=48 - 49, Safari <=9.0 - 9.1
// focus(in | out) events fire after focus & blur events,
// which is spec violation - http://www.w3.org/TR/DOM-Level-3-Events/#events-focusevent-event-order
// Related ticket - https://bugs.chromium.org/p/chromium/issues/detail?id=449857
if ( !support.focusin ) {
	jQuery.each( { focus: "focusin", blur: "focusout" }, function( orig, fix ) {

		// Attach a single capturing handler on the document while someone wants focusin/focusout
		var handler = function( event ) {
			jQuery.event.simulate( fix, event.target, jQuery.event.fix( event ) );
		};

		jQuery.event.special[ fix ] = {
			setup: function() {

				// Handle: regular nodes (via `this.ownerDocument`), window
				// (via `this.document`) & document (via `this`).
				var doc = this.ownerDocument || this.document || this,
					attaches = dataPriv.access( doc, fix );

				if ( !attaches ) {
					doc.addEventListener( orig, handler, true );
				}
				dataPriv.access( doc, fix, ( attaches || 0 ) + 1 );
			},
			teardown: function() {
				var doc = this.ownerDocument || this.document || this,
					attaches = dataPriv.access( doc, fix ) - 1;

				if ( !attaches ) {
					doc.removeEventListener( orig, handler, true );
					dataPriv.remove( doc, fix );

				} else {
					dataPriv.access( doc, fix, attaches );
				}
			}
		};
	} );
}
var location = window.location;

var nonce = { guid: Date.now() };

var rquery = ( /\?/ );



// Cross-browser xml parsing
jQuery.parseXML = function( data ) {
	var xml;
	if ( !data || typeof data !== "string" ) {
		return null;
	}

	// Support: IE 9 - 11 only
	// IE throws on parseFromString with invalid input.
	try {
		xml = ( new window.DOMParser() ).parseFromString( data, "text/xml" );
	} catch ( e ) {
		xml = undefined;
	}

	if ( !xml || xml.getElementsByTagName( "parsererror" ).length ) {
		jQuery.error( "Invalid XML: " + data );
	}
	return xml;
};


var
	rbracket = /\[\]$/,
	rCRLF = /\r?\n/g,
	rsubmitterTypes = /^(?:submit|button|image|reset|file)$/i,
	rsubmittable = /^(?:input|select|textarea|keygen)/i;

function buildParams( prefix, obj, traditional, add ) {
	var name;

	if ( Array.isArray( obj ) ) {

		// Serialize array item.
		jQuery.each( obj, function( i, v ) {
			if ( traditional || rbracket.test( prefix ) ) {

				// Treat each array item as a scalar.
				add( prefix, v );

			} else {

				// Item is non-scalar (array or object), encode its numeric index.
				buildParams(
					prefix + "[" + ( typeof v === "object" && v != null ? i : "" ) + "]",
					v,
					traditional,
					add
				);
			}
		} );

	} else if ( !traditional && toType( obj ) === "object" ) {

		// Serialize object item.
		for ( name in obj ) {
			buildParams( prefix + "[" + name + "]", obj[ name ], traditional, add );
		}

	} else {

		// Serialize scalar item.
		add( prefix, obj );
	}
}

// Serialize an array of form elements or a set of
// key/values into a query string
jQuery.param = function( a, traditional ) {
	var prefix,
		s = [],
		add = function( key, valueOrFunction ) {

			// If value is a function, invoke it and use its return value
			var value = isFunction( valueOrFunction ) ?
				valueOrFunction() :
				valueOrFunction;

			s[ s.length ] = encodeURIComponent( key ) + "=" +
				encodeURIComponent( value == null ? "" : value );
		};

	if ( a == null ) {
		return "";
	}

	// If an array was passed in, assume that it is an array of form elements.
	if ( Array.isArray( a ) || ( a.jquery && !jQuery.isPlainObject( a ) ) ) {

		// Serialize the form elements
		jQuery.each( a, function() {
			add( this.name, this.value );
		} );

	} else {

		// If traditional, encode the "old" way (the way 1.3.2 or older
		// did it), otherwise encode params recursively.
		for ( prefix in a ) {
			buildParams( prefix, a[ prefix ], traditional, add );
		}
	}

	// Return the resulting serialization
	return s.join( "&" );
};

jQuery.fn.extend( {
	serialize: function() {
		return jQuery.param( this.serializeArray() );
	},
	serializeArray: function() {
		return this.map( function() {

			// Can add propHook for "elements" to filter or add form elements
			var elements = jQuery.prop( this, "elements" );
			return elements ? jQuery.makeArray( elements ) : this;
		} )
		.filter( function() {
			var type = this.type;

			// Use .is( ":disabled" ) so that fieldset[disabled] works
			return this.name && !jQuery( this ).is( ":disabled" ) &&
				rsubmittable.test( this.nodeName ) && !rsubmitterTypes.test( type ) &&
				( this.checked || !rcheckableType.test( type ) );
		} )
		.map( function( _i, elem ) {
			var val = jQuery( this ).val();

			if ( val == null ) {
				return null;
			}

			if ( Array.isArray( val ) ) {
				return jQuery.map( val, function( val ) {
					return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
				} );
			}

			return { name: elem.name, value: val.replace( rCRLF, "\r\n" ) };
		} ).get();
	}
} );


var
	r20 = /%20/g,
	rhash = /#.*$/,
	rantiCache = /([?&])_=[^&]*/,
	rheaders = /^(.*?):[ \t]*([^\r\n]*)$/mg,

	// #7653, #8125, #8152: local protocol detection
	rlocalProtocol = /^(?:about|app|app-storage|.+-extension|file|res|widget):$/,
	rnoContent = /^(?:GET|HEAD)$/,
	rprotocol = /^\/\//,

	/* Prefilters
	 * 1) They are useful to introduce custom dataTypes (see ajax/jsonp.js for an example)
	 * 2) These are called:
	 *    - BEFORE asking for a transport
	 *    - AFTER param serialization (s.data is a string if s.processData is true)
	 * 3) key is the dataType
	 * 4) the catchall symbol "*" can be used
	 * 5) execution will start with transport dataType and THEN continue down to "*" if needed
	 */
	prefilters = {},

	/* Transports bindings
	 * 1) key is the dataType
	 * 2) the catchall symbol "*" can be used
	 * 3) selection will start with transport dataType and THEN go to "*" if needed
	 */
	transports = {},

	// Avoid comment-prolog char sequence (#10098); must appease lint and evade compression
	allTypes = "*/".concat( "*" ),

	// Anchor tag for parsing the document origin
	originAnchor = document.createElement( "a" );
	originAnchor.href = location.href;

// Base "constructor" for jQuery.ajaxPrefilter and jQuery.ajaxTransport
function addToPrefiltersOrTransports( structure ) {

	// dataTypeExpression is optional and defaults to "*"
	return function( dataTypeExpression, func ) {

		if ( typeof dataTypeExpression !== "string" ) {
			func = dataTypeExpression;
			dataTypeExpression = "*";
		}

		var dataType,
			i = 0,
			dataTypes = dataTypeExpression.toLowerCase().match( rnothtmlwhite ) || [];

		if ( isFunction( func ) ) {

			// For each dataType in the dataTypeExpression
			while ( ( dataType = dataTypes[ i++ ] ) ) {

				// Prepend if requested
				if ( dataType[ 0 ] === "+" ) {
					dataType = dataType.slice( 1 ) || "*";
					( structure[ dataType ] = structure[ dataType ] || [] ).unshift( func );

				// Otherwise append
				} else {
					( structure[ dataType ] = structure[ dataType ] || [] ).push( func );
				}
			}
		}
	};
}

// Base inspection function for prefilters and transports
function inspectPrefiltersOrTransports( structure, options, originalOptions, jqXHR ) {

	var inspected = {},
		seekingTransport = ( structure === transports );

	function inspect( dataType ) {
		var selected;
		inspected[ dataType ] = true;
		jQuery.each( structure[ dataType ] || [], function( _, prefilterOrFactory ) {
			var dataTypeOrTransport = prefilterOrFactory( options, originalOptions, jqXHR );
			if ( typeof dataTypeOrTransport === "string" &&
				!seekingTransport && !inspected[ dataTypeOrTransport ] ) {

				options.dataTypes.unshift( dataTypeOrTransport );
				inspect( dataTypeOrTransport );
				return false;
			} else if ( seekingTransport ) {
				return !( selected = dataTypeOrTransport );
			}
		} );
		return selected;
	}

	return inspect( options.dataTypes[ 0 ] ) || !inspected[ "*" ] && inspect( "*" );
}

// A special extend for ajax options
// that takes "flat" options (not to be deep extended)
// Fixes #9887
function ajaxExtend( target, src ) {
	var key, deep,
		flatOptions = jQuery.ajaxSettings.flatOptions || {};

	for ( key in src ) {
		if ( src[ key ] !== undefined ) {
			( flatOptions[ key ] ? target : ( deep || ( deep = {} ) ) )[ key ] = src[ key ];
		}
	}
	if ( deep ) {
		jQuery.extend( true, target, deep );
	}

	return target;
}

/* Handles responses to an ajax request:
 * - finds the right dataType (mediates between content-type and expected dataType)
 * - returns the corresponding response
 */
function ajaxHandleResponses( s, jqXHR, responses ) {

	var ct, type, finalDataType, firstDataType,
		contents = s.contents,
		dataTypes = s.dataTypes;

	// Remove auto dataType and get content-type in the process
	while ( dataTypes[ 0 ] === "*" ) {
		dataTypes.shift();
		if ( ct === undefined ) {
			ct = s.mimeType || jqXHR.getResponseHeader( "Content-Type" );
		}
	}

	// Check if we're dealing with a known content-type
	if ( ct ) {
		for ( type in contents ) {
			if ( contents[ type ] && contents[ type ].test( ct ) ) {
				dataTypes.unshift( type );
				break;
			}
		}
	}

	// Check to see if we have a response for the expected dataType
	if ( dataTypes[ 0 ] in responses ) {
		finalDataType = dataTypes[ 0 ];
	} else {

		// Try convertible dataTypes
		for ( type in responses ) {
			if ( !dataTypes[ 0 ] || s.converters[ type + " " + dataTypes[ 0 ] ] ) {
				finalDataType = type;
				break;
			}
			if ( !firstDataType ) {
				firstDataType = type;
			}
		}

		// Or just use first one
		finalDataType = finalDataType || firstDataType;
	}

	// If we found a dataType
	// We add the dataType to the list if needed
	// and return the corresponding response
	if ( finalDataType ) {
		if ( finalDataType !== dataTypes[ 0 ] ) {
			dataTypes.unshift( finalDataType );
		}
		return responses[ finalDataType ];
	}
}

/* Chain conversions given the request and the original response
 * Also sets the responseXXX fields on the jqXHR instance
 */
function ajaxConvert( s, response, jqXHR, isSuccess ) {
	var conv2, current, conv, tmp, prev,
		converters = {},

		// Work with a copy of dataTypes in case we need to modify it for conversion
		dataTypes = s.dataTypes.slice();

	// Create converters map with lowercased keys
	if ( dataTypes[ 1 ] ) {
		for ( conv in s.converters ) {
			converters[ conv.toLowerCase() ] = s.converters[ conv ];
		}
	}

	current = dataTypes.shift();

	// Convert to each sequential dataType
	while ( current ) {

		if ( s.responseFields[ current ] ) {
			jqXHR[ s.responseFields[ current ] ] = response;
		}

		// Apply the dataFilter if provided
		if ( !prev && isSuccess && s.dataFilter ) {
			response = s.dataFilter( response, s.dataType );
		}

		prev = current;
		current = dataTypes.shift();

		if ( current ) {

			// There's only work to do if current dataType is non-auto
			if ( current === "*" ) {

				current = prev;

			// Convert response if prev dataType is non-auto and differs from current
			} else if ( prev !== "*" && prev !== current ) {

				// Seek a direct converter
				conv = converters[ prev + " " + current ] || converters[ "* " + current ];

				// If none found, seek a pair
				if ( !conv ) {
					for ( conv2 in converters ) {

						// If conv2 outputs current
						tmp = conv2.split( " " );
						if ( tmp[ 1 ] === current ) {

							// If prev can be converted to accepted input
							conv = converters[ prev + " " + tmp[ 0 ] ] ||
								converters[ "* " + tmp[ 0 ] ];
							if ( conv ) {

								// Condense equivalence converters
								if ( conv === true ) {
									conv = converters[ conv2 ];

								// Otherwise, insert the intermediate dataType
								} else if ( converters[ conv2 ] !== true ) {
									current = tmp[ 0 ];
									dataTypes.unshift( tmp[ 1 ] );
								}
								break;
							}
						}
					}
				}

				// Apply converter (if not an equivalence)
				if ( conv !== true ) {

					// Unless errors are allowed to bubble, catch and return them
					if ( conv && s.throws ) {
						response = conv( response );
					} else {
						try {
							response = conv( response );
						} catch ( e ) {
							return {
								state: "parsererror",
								error: conv ? e : "No conversion from " + prev + " to " + current
							};
						}
					}
				}
			}
		}
	}

	return { state: "success", data: response };
}

jQuery.extend( {

	// Counter for holding the number of active queries
	active: 0,

	// Last-Modified header cache for next request
	lastModified: {},
	etag: {},

	ajaxSettings: {
		url: location.href,
		type: "GET",
		isLocal: rlocalProtocol.test( location.protocol ),
		global: true,
		processData: true,
		async: true,
		contentType: "application/x-www-form-urlencoded; charset=UTF-8",

		/*
		timeout: 0,
		data: null,
		dataType: null,
		username: null,
		password: null,
		cache: null,
		throws: false,
		traditional: false,
		headers: {},
		*/

		accepts: {
			"*": allTypes,
			text: "text/plain",
			html: "text/html",
			xml: "application/xml, text/xml",
			json: "application/json, text/javascript"
		},

		contents: {
			xml: /\bxml\b/,
			html: /\bhtml/,
			json: /\bjson\b/
		},

		responseFields: {
			xml: "responseXML",
			text: "responseText",
			json: "responseJSON"
		},

		// Data converters
		// Keys separate source (or catchall "*") and destination types with a single space
		converters: {

			// Convert anything to text
			"* text": String,

			// Text to html (true = no transformation)
			"text html": true,

			// Evaluate text as a json expression
			"text json": JSON.parse,

			// Parse text as xml
			"text xml": jQuery.parseXML
		},

		// For options that shouldn't be deep extended:
		// you can add your own custom options here if
		// and when you create one that shouldn't be
		// deep extended (see ajaxExtend)
		flatOptions: {
			url: true,
			context: true
		}
	},

	// Creates a full fledged settings object into target
	// with both ajaxSettings and settings fields.
	// If target is omitted, writes into ajaxSettings.
	ajaxSetup: function( target, settings ) {
		return settings ?

			// Building a settings object
			ajaxExtend( ajaxExtend( target, jQuery.ajaxSettings ), settings ) :

			// Extending ajaxSettings
			ajaxExtend( jQuery.ajaxSettings, target );
	},

	ajaxPrefilter: addToPrefiltersOrTransports( prefilters ),
	ajaxTransport: addToPrefiltersOrTransports( transports ),

	// Main method
	ajax: function( url, options ) {

		// If url is an object, simulate pre-1.5 signature
		if ( typeof url === "object" ) {
			options = url;
			url = undefined;
		}

		// Force options to be an object
		options = options || {};

		var transport,

			// URL without anti-cache param
			cacheURL,

			// Response headers
			responseHeadersString,
			responseHeaders,

			// timeout handle
			timeoutTimer,

			// Url cleanup var
			urlAnchor,

			// Request state (becomes false upon send and true upon completion)
			completed,

			// To know if global events are to be dispatched
			fireGlobals,

			// Loop variable
			i,

			// uncached part of the url
			uncached,

			// Create the final options object
			s = jQuery.ajaxSetup( {}, options ),

			// Callbacks context
			callbackContext = s.context || s,

			// Context for global events is callbackContext if it is a DOM node or jQuery collection
			globalEventContext = s.context &&
				( callbackContext.nodeType || callbackContext.jquery ) ?
					jQuery( callbackContext ) :
					jQuery.event,

			// Deferreds
			deferred = jQuery.Deferred(),
			completeDeferred = jQuery.Callbacks( "once memory" ),

			// Status-dependent callbacks
			statusCode = s.statusCode || {},

			// Headers (they are sent all at once)
			requestHeaders = {},
			requestHeadersNames = {},

			// Default abort message
			strAbort = "canceled",

			// Fake xhr
			jqXHR = {
				readyState: 0,

				// Builds headers hashtable if needed
				getResponseHeader: function( key ) {
					var match;
					if ( completed ) {
						if ( !responseHeaders ) {
							responseHeaders = {};
							while ( ( match = rheaders.exec( responseHeadersString ) ) ) {
								responseHeaders[ match[ 1 ].toLowerCase() + " " ] =
									( responseHeaders[ match[ 1 ].toLowerCase() + " " ] || [] )
										.concat( match[ 2 ] );
							}
						}
						match = responseHeaders[ key.toLowerCase() + " " ];
					}
					return match == null ? null : match.join( ", " );
				},

				// Raw string
				getAllResponseHeaders: function() {
					return completed ? responseHeadersString : null;
				},

				// Caches the header
				setRequestHeader: function( name, value ) {
					if ( completed == null ) {
						name = requestHeadersNames[ name.toLowerCase() ] =
							requestHeadersNames[ name.toLowerCase() ] || name;
						requestHeaders[ name ] = value;
					}
					return this;
				},

				// Overrides response content-type header
				overrideMimeType: function( type ) {
					if ( completed == null ) {
						s.mimeType = type;
					}
					return this;
				},

				// Status-dependent callbacks
				statusCode: function( map ) {
					var code;
					if ( map ) {
						if ( completed ) {

							// Execute the appropriate callbacks
							jqXHR.always( map[ jqXHR.status ] );
						} else {

							// Lazy-add the new callbacks in a way that preserves old ones
							for ( code in map ) {
								statusCode[ code ] = [ statusCode[ code ], map[ code ] ];
							}
						}
					}
					return this;
				},

				// Cancel the request
				abort: function( statusText ) {
					var finalText = statusText || strAbort;
					if ( transport ) {
						transport.abort( finalText );
					}
					done( 0, finalText );
					return this;
				}
			};

		// Attach deferreds
		deferred.promise( jqXHR );

		// Add protocol if not provided (prefilters might expect it)
		// Handle falsy url in the settings object (#10093: consistency with old signature)
		// We also use the url parameter if available
		s.url = ( ( url || s.url || location.href ) + "" )
			.replace( rprotocol, location.protocol + "//" );

		// Alias method option to type as per ticket #12004
		s.type = options.method || options.type || s.method || s.type;

		// Extract dataTypes list
		s.dataTypes = ( s.dataType || "*" ).toLowerCase().match( rnothtmlwhite ) || [ "" ];

		// A cross-domain request is in order when the origin doesn't match the current origin.
		if ( s.crossDomain == null ) {
			urlAnchor = document.createElement( "a" );

			// Support: IE <=8 - 11, Edge 12 - 15
			// IE throws exception on accessing the href property if url is malformed,
			// e.g. http://example.com:80x/
			try {
				urlAnchor.href = s.url;

				// Support: IE <=8 - 11 only
				// Anchor's host property isn't correctly set when s.url is relative
				urlAnchor.href = urlAnchor.href;
				s.crossDomain = originAnchor.protocol + "//" + originAnchor.host !==
					urlAnchor.protocol + "//" + urlAnchor.host;
			} catch ( e ) {

				// If there is an error parsing the URL, assume it is crossDomain,
				// it can be rejected by the transport if it is invalid
				s.crossDomain = true;
			}
		}

		// Convert data if not already a string
		if ( s.data && s.processData && typeof s.data !== "string" ) {
			s.data = jQuery.param( s.data, s.traditional );
		}

		// Apply prefilters
		inspectPrefiltersOrTransports( prefilters, s, options, jqXHR );

		// If request was aborted inside a prefilter, stop there
		if ( completed ) {
			return jqXHR;
		}

		// We can fire global events as of now if asked to
		// Don't fire events if jQuery.event is undefined in an AMD-usage scenario (#15118)
		fireGlobals = jQuery.event && s.global;

		// Watch for a new set of requests
		if ( fireGlobals && jQuery.active++ === 0 ) {
			jQuery.event.trigger( "ajaxStart" );
		}

		// Uppercase the type
		s.type = s.type.toUpperCase();

		// Determine if request has content
		s.hasContent = !rnoContent.test( s.type );

		// Save the URL in case we're toying with the If-Modified-Since
		// and/or If-None-Match header later on
		// Remove hash to simplify url manipulation
		cacheURL = s.url.replace( rhash, "" );

		// More options handling for requests with no content
		if ( !s.hasContent ) {

			// Remember the hash so we can put it back
			uncached = s.url.slice( cacheURL.length );

			// If data is available and should be processed, append data to url
			if ( s.data && ( s.processData || typeof s.data === "string" ) ) {
				cacheURL += ( rquery.test( cacheURL ) ? "&" : "?" ) + s.data;

				// #9682: remove data so that it's not used in an eventual retry
				delete s.data;
			}

			// Add or update anti-cache param if needed
			if ( s.cache === false ) {
				cacheURL = cacheURL.replace( rantiCache, "$1" );
				uncached = ( rquery.test( cacheURL ) ? "&" : "?" ) + "_=" + ( nonce.guid++ ) +
					uncached;
			}

			// Put hash and anti-cache on the URL that will be requested (gh-1732)
			s.url = cacheURL + uncached;

		// Change '%20' to '+' if this is encoded form body content (gh-2658)
		} else if ( s.data && s.processData &&
			( s.contentType || "" ).indexOf( "application/x-www-form-urlencoded" ) === 0 ) {
			s.data = s.data.replace( r20, "+" );
		}

		// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
		if ( s.ifModified ) {
			if ( jQuery.lastModified[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-Modified-Since", jQuery.lastModified[ cacheURL ] );
			}
			if ( jQuery.etag[ cacheURL ] ) {
				jqXHR.setRequestHeader( "If-None-Match", jQuery.etag[ cacheURL ] );
			}
		}

		// Set the correct header, if data is being sent
		if ( s.data && s.hasContent && s.contentType !== false || options.contentType ) {
			jqXHR.setRequestHeader( "Content-Type", s.contentType );
		}

		// Set the Accepts header for the server, depending on the dataType
		jqXHR.setRequestHeader(
			"Accept",
			s.dataTypes[ 0 ] && s.accepts[ s.dataTypes[ 0 ] ] ?
				s.accepts[ s.dataTypes[ 0 ] ] +
					( s.dataTypes[ 0 ] !== "*" ? ", " + allTypes + "; q=0.01" : "" ) :
				s.accepts[ "*" ]
		);

		// Check for headers option
		for ( i in s.headers ) {
			jqXHR.setRequestHeader( i, s.headers[ i ] );
		}

		// Allow custom headers/mimetypes and early abort
		if ( s.beforeSend &&
			( s.beforeSend.call( callbackContext, jqXHR, s ) === false || completed ) ) {

			// Abort if not done already and return
			return jqXHR.abort();
		}

		// Aborting is no longer a cancellation
		strAbort = "abort";

		// Install callbacks on deferreds
		completeDeferred.add( s.complete );
		jqXHR.done( s.success );
		jqXHR.fail( s.error );

		// Get transport
		transport = inspectPrefiltersOrTransports( transports, s, options, jqXHR );

		// If no transport, we auto-abort
		if ( !transport ) {
			done( -1, "No Transport" );
		} else {
			jqXHR.readyState = 1;

			// Send global event
			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxSend", [ jqXHR, s ] );
			}

			// If request was aborted inside ajaxSend, stop there
			if ( completed ) {
				return jqXHR;
			}

			// Timeout
			if ( s.async && s.timeout > 0 ) {
				timeoutTimer = window.setTimeout( function() {
					jqXHR.abort( "timeout" );
				}, s.timeout );
			}

			try {
				completed = false;
				transport.send( requestHeaders, done );
			} catch ( e ) {

				// Rethrow post-completion exceptions
				if ( completed ) {
					throw e;
				}

				// Propagate others as results
				done( -1, e );
			}
		}

		// Callback for when everything is done
		function done( status, nativeStatusText, responses, headers ) {
			var isSuccess, success, error, response, modified,
				statusText = nativeStatusText;

			// Ignore repeat invocations
			if ( completed ) {
				return;
			}

			completed = true;

			// Clear timeout if it exists
			if ( timeoutTimer ) {
				window.clearTimeout( timeoutTimer );
			}

			// Dereference transport for early garbage collection
			// (no matter how long the jqXHR object will be used)
			transport = undefined;

			// Cache response headers
			responseHeadersString = headers || "";

			// Set readyState
			jqXHR.readyState = status > 0 ? 4 : 0;

			// Determine if successful
			isSuccess = status >= 200 && status < 300 || status === 304;

			// Get response data
			if ( responses ) {
				response = ajaxHandleResponses( s, jqXHR, responses );
			}

			// Use a noop converter for missing script
			if ( !isSuccess && jQuery.inArray( "script", s.dataTypes ) > -1 ) {
				s.converters[ "text script" ] = function() {};
			}

			// Convert no matter what (that way responseXXX fields are always set)
			response = ajaxConvert( s, response, jqXHR, isSuccess );

			// If successful, handle type chaining
			if ( isSuccess ) {

				// Set the If-Modified-Since and/or If-None-Match header, if in ifModified mode.
				if ( s.ifModified ) {
					modified = jqXHR.getResponseHeader( "Last-Modified" );
					if ( modified ) {
						jQuery.lastModified[ cacheURL ] = modified;
					}
					modified = jqXHR.getResponseHeader( "etag" );
					if ( modified ) {
						jQuery.etag[ cacheURL ] = modified;
					}
				}

				// if no content
				if ( status === 204 || s.type === "HEAD" ) {
					statusText = "nocontent";

				// if not modified
				} else if ( status === 304 ) {
					statusText = "notmodified";

				// If we have data, let's convert it
				} else {
					statusText = response.state;
					success = response.data;
					error = response.error;
					isSuccess = !error;
				}
			} else {

				// Extract error from statusText and normalize for non-aborts
				error = statusText;
				if ( status || !statusText ) {
					statusText = "error";
					if ( status < 0 ) {
						status = 0;
					}
				}
			}

			// Set data for the fake xhr object
			jqXHR.status = status;
			jqXHR.statusText = ( nativeStatusText || statusText ) + "";

			// Success/Error
			if ( isSuccess ) {
				deferred.resolveWith( callbackContext, [ success, statusText, jqXHR ] );
			} else {
				deferred.rejectWith( callbackContext, [ jqXHR, statusText, error ] );
			}

			// Status-dependent callbacks
			jqXHR.statusCode( statusCode );
			statusCode = undefined;

			if ( fireGlobals ) {
				globalEventContext.trigger( isSuccess ? "ajaxSuccess" : "ajaxError",
					[ jqXHR, s, isSuccess ? success : error ] );
			}

			// Complete
			completeDeferred.fireWith( callbackContext, [ jqXHR, statusText ] );

			if ( fireGlobals ) {
				globalEventContext.trigger( "ajaxComplete", [ jqXHR, s ] );

				// Handle the global AJAX counter
				if ( !( --jQuery.active ) ) {
					jQuery.event.trigger( "ajaxStop" );
				}
			}
		}

		return jqXHR;
	},

	getJSON: function( url, data, callback ) {
		return jQuery.get( url, data, callback, "json" );
	},

	getScript: function( url, callback ) {
		return jQuery.get( url, undefined, callback, "script" );
	}
} );

jQuery.each( [ "get", "post" ], function( _i, method ) {
	jQuery[ method ] = function( url, data, callback, type ) {

		// Shift arguments if data argument was omitted
		if ( isFunction( data ) ) {
			type = type || callback;
			callback = data;
			data = undefined;
		}

		// The url can be an options object (which then must have .url)
		return jQuery.ajax( jQuery.extend( {
			url: url,
			type: method,
			dataType: type,
			data: data,
			success: callback
		}, jQuery.isPlainObject( url ) && url ) );
	};
} );

jQuery.ajaxPrefilter( function( s ) {
	var i;
	for ( i in s.headers ) {
		if ( i.toLowerCase() === "content-type" ) {
			s.contentType = s.headers[ i ] || "";
		}
	}
} );


jQuery._evalUrl = function( url, options, doc ) {
	return jQuery.ajax( {
		url: url,

		// Make this explicit, since user can override this through ajaxSetup (#11264)
		type: "GET",
		dataType: "script",
		cache: true,
		async: false,
		global: false,

		// Only evaluate the response if it is successful (gh-4126)
		// dataFilter is not invoked for failure responses, so using it instead
		// of the default converter is kludgy but it works.
		converters: {
			"text script": function() {}
		},
		dataFilter: function( response ) {
			jQuery.globalEval( response, options, doc );
		}
	} );
};


jQuery.fn.extend( {
	wrapAll: function( html ) {
		var wrap;

		if ( this[ 0 ] ) {
			if ( isFunction( html ) ) {
				html = html.call( this[ 0 ] );
			}

			// The elements to wrap the target around
			wrap = jQuery( html, this[ 0 ].ownerDocument ).eq( 0 ).clone( true );

			if ( this[ 0 ].parentNode ) {
				wrap.insertBefore( this[ 0 ] );
			}

			wrap.map( function() {
				var elem = this;

				while ( elem.firstElementChild ) {
					elem = elem.firstElementChild;
				}

				return elem;
			} ).append( this );
		}

		return this;
	},

	wrapInner: function( html ) {
		if ( isFunction( html ) ) {
			return this.each( function( i ) {
				jQuery( this ).wrapInner( html.call( this, i ) );
			} );
		}

		return this.each( function() {
			var self = jQuery( this ),
				contents = self.contents();

			if ( contents.length ) {
				contents.wrapAll( html );

			} else {
				self.append( html );
			}
		} );
	},

	wrap: function( html ) {
		var htmlIsFunction = isFunction( html );

		return this.each( function( i ) {
			jQuery( this ).wrapAll( htmlIsFunction ? html.call( this, i ) : html );
		} );
	},

	unwrap: function( selector ) {
		this.parent( selector ).not( "body" ).each( function() {
			jQuery( this ).replaceWith( this.childNodes );
		} );
		return this;
	}
} );


jQuery.expr.pseudos.hidden = function( elem ) {
	return !jQuery.expr.pseudos.visible( elem );
};
jQuery.expr.pseudos.visible = function( elem ) {
	return !!( elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length );
};




jQuery.ajaxSettings.xhr = function() {
	try {
		return new window.XMLHttpRequest();
	} catch ( e ) {}
};

var xhrSuccessStatus = {

		// File protocol always yields status code 0, assume 200
		0: 200,

		// Support: IE <=9 only
		// #1450: sometimes IE returns 1223 when it should be 204
		1223: 204
	},
	xhrSupported = jQuery.ajaxSettings.xhr();

support.cors = !!xhrSupported && ( "withCredentials" in xhrSupported );
support.ajax = xhrSupported = !!xhrSupported;

jQuery.ajaxTransport( function( options ) {
	var callback, errorCallback;

	// Cross domain only allowed if supported through XMLHttpRequest
	if ( support.cors || xhrSupported && !options.crossDomain ) {
		return {
			send: function( headers, complete ) {
				var i,
					xhr = options.xhr();

				xhr.open(
					options.type,
					options.url,
					options.async,
					options.username,
					options.password
				);

				// Apply custom fields if provided
				if ( options.xhrFields ) {
					for ( i in options.xhrFields ) {
						xhr[ i ] = options.xhrFields[ i ];
					}
				}

				// Override mime type if needed
				if ( options.mimeType && xhr.overrideMimeType ) {
					xhr.overrideMimeType( options.mimeType );
				}

				// X-Requested-With header
				// For cross-domain requests, seeing as conditions for a preflight are
				// akin to a jigsaw puzzle, we simply never set it to be sure.
				// (it can always be set on a per-request basis or even using ajaxSetup)
				// For same-domain requests, won't change header if already provided.
				if ( !options.crossDomain && !headers[ "X-Requested-With" ] ) {
					headers[ "X-Requested-With" ] = "XMLHttpRequest";
				}

				// Set headers
				for ( i in headers ) {
					xhr.setRequestHeader( i, headers[ i ] );
				}

				// Callback
				callback = function( type ) {
					return function() {
						if ( callback ) {
							callback = errorCallback = xhr.onload =
								xhr.onerror = xhr.onabort = xhr.ontimeout =
									xhr.onreadystatechange = null;

							if ( type === "abort" ) {
								xhr.abort();
							} else if ( type === "error" ) {

								// Support: IE <=9 only
								// On a manual native abort, IE9 throws
								// errors on any property access that is not readyState
								if ( typeof xhr.status !== "number" ) {
									complete( 0, "error" );
								} else {
									complete(

										// File: protocol always yields status 0; see #8605, #14207
										xhr.status,
										xhr.statusText
									);
								}
							} else {
								complete(
									xhrSuccessStatus[ xhr.status ] || xhr.status,
									xhr.statusText,

									// Support: IE <=9 only
									// IE9 has no XHR2 but throws on binary (trac-11426)
									// For XHR2 non-text, let the caller handle it (gh-2498)
									( xhr.responseType || "text" ) !== "text"  ||
									typeof xhr.responseText !== "string" ?
										{ binary: xhr.response } :
										{ text: xhr.responseText },
									xhr.getAllResponseHeaders()
								);
							}
						}
					};
				};

				// Listen to events
				xhr.onload = callback();
				errorCallback = xhr.onerror = xhr.ontimeout = callback( "error" );

				// Support: IE 9 only
				// Use onreadystatechange to replace onabort
				// to handle uncaught aborts
				if ( xhr.onabort !== undefined ) {
					xhr.onabort = errorCallback;
				} else {
					xhr.onreadystatechange = function() {

						// Check readyState before timeout as it changes
						if ( xhr.readyState === 4 ) {

							// Allow onerror to be called first,
							// but that will not handle a native abort
							// Also, save errorCallback to a variable
							// as xhr.onerror cannot be accessed
							window.setTimeout( function() {
								if ( callback ) {
									errorCallback();
								}
							} );
						}
					};
				}

				// Create the abort callback
				callback = callback( "abort" );

				try {

					// Do send the request (this may raise an exception)
					xhr.send( options.hasContent && options.data || null );
				} catch ( e ) {

					// #14683: Only rethrow if this hasn't been notified as an error yet
					if ( callback ) {
						throw e;
					}
				}
			},

			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




// Prevent auto-execution of scripts when no explicit dataType was provided (See gh-2432)
jQuery.ajaxPrefilter( function( s ) {
	if ( s.crossDomain ) {
		s.contents.script = false;
	}
} );

// Install script dataType
jQuery.ajaxSetup( {
	accepts: {
		script: "text/javascript, application/javascript, " +
			"application/ecmascript, application/x-ecmascript"
	},
	contents: {
		script: /\b(?:java|ecma)script\b/
	},
	converters: {
		"text script": function( text ) {
			jQuery.globalEval( text );
			return text;
		}
	}
} );

// Handle cache's special case and crossDomain
jQuery.ajaxPrefilter( "script", function( s ) {
	if ( s.cache === undefined ) {
		s.cache = false;
	}
	if ( s.crossDomain ) {
		s.type = "GET";
	}
} );

// Bind script tag hack transport
jQuery.ajaxTransport( "script", function( s ) {

	// This transport only deals with cross domain or forced-by-attrs requests
	if ( s.crossDomain || s.scriptAttrs ) {
		var script, callback;
		return {
			send: function( _, complete ) {
				script = jQuery( "<script>" )
					.attr( s.scriptAttrs || {} )
					.prop( { charset: s.scriptCharset, src: s.url } )
					.on( "load error", callback = function( evt ) {
						script.remove();
						callback = null;
						if ( evt ) {
							complete( evt.type === "error" ? 404 : 200, evt.type );
						}
					} );

				// Use native DOM manipulation to avoid our domManip AJAX trickery
				document.head.appendChild( script[ 0 ] );
			},
			abort: function() {
				if ( callback ) {
					callback();
				}
			}
		};
	}
} );




var oldCallbacks = [],
	rjsonp = /(=)\?(?=&|$)|\?\?/;

// Default jsonp settings
jQuery.ajaxSetup( {
	jsonp: "callback",
	jsonpCallback: function() {
		var callback = oldCallbacks.pop() || ( jQuery.expando + "_" + ( nonce.guid++ ) );
		this[ callback ] = true;
		return callback;
	}
} );

// Detect, normalize options and install callbacks for jsonp requests
jQuery.ajaxPrefilter( "json jsonp", function( s, originalSettings, jqXHR ) {

	var callbackName, overwritten, responseContainer,
		jsonProp = s.jsonp !== false && ( rjsonp.test( s.url ) ?
			"url" :
			typeof s.data === "string" &&
				( s.contentType || "" )
					.indexOf( "application/x-www-form-urlencoded" ) === 0 &&
				rjsonp.test( s.data ) && "data"
		);

	// Handle iff the expected data type is "jsonp" or we have a parameter to set
	if ( jsonProp || s.dataTypes[ 0 ] === "jsonp" ) {

		// Get callback name, remembering preexisting value associated with it
		callbackName = s.jsonpCallback = isFunction( s.jsonpCallback ) ?
			s.jsonpCallback() :
			s.jsonpCallback;

		// Insert callback into url or form data
		if ( jsonProp ) {
			s[ jsonProp ] = s[ jsonProp ].replace( rjsonp, "$1" + callbackName );
		} else if ( s.jsonp !== false ) {
			s.url += ( rquery.test( s.url ) ? "&" : "?" ) + s.jsonp + "=" + callbackName;
		}

		// Use data converter to retrieve json after script execution
		s.converters[ "script json" ] = function() {
			if ( !responseContainer ) {
				jQuery.error( callbackName + " was not called" );
			}
			return responseContainer[ 0 ];
		};

		// Force json dataType
		s.dataTypes[ 0 ] = "json";

		// Install callback
		overwritten = window[ callbackName ];
		window[ callbackName ] = function() {
			responseContainer = arguments;
		};

		// Clean-up function (fires after converters)
		jqXHR.always( function() {

			// If previous value didn't exist - remove it
			if ( overwritten === undefined ) {
				jQuery( window ).removeProp( callbackName );

			// Otherwise restore preexisting value
			} else {
				window[ callbackName ] = overwritten;
			}

			// Save back as free
			if ( s[ callbackName ] ) {

				// Make sure that re-using the options doesn't screw things around
				s.jsonpCallback = originalSettings.jsonpCallback;

				// Save the callback name for future use
				oldCallbacks.push( callbackName );
			}

			// Call if it was a function and we have a response
			if ( responseContainer && isFunction( overwritten ) ) {
				overwritten( responseContainer[ 0 ] );
			}

			responseContainer = overwritten = undefined;
		} );

		// Delegate to script
		return "script";
	}
} );




// Support: Safari 8 only
// In Safari 8 documents created via document.implementation.createHTMLDocument
// collapse sibling forms: the second one becomes a child of the first one.
// Because of that, this security measure has to be disabled in Safari 8.
// https://bugs.webkit.org/show_bug.cgi?id=137337
support.createHTMLDocument = ( function() {
	var body = document.implementation.createHTMLDocument( "" ).body;
	body.innerHTML = "<form></form><form></form>";
	return body.childNodes.length === 2;
} )();


// Argument "data" should be string of html
// context (optional): If specified, the fragment will be created in this context,
// defaults to document
// keepScripts (optional): If true, will include scripts passed in the html string
jQuery.parseHTML = function( data, context, keepScripts ) {
	if ( typeof data !== "string" ) {
		return [];
	}
	if ( typeof context === "boolean" ) {
		keepScripts = context;
		context = false;
	}

	var base, parsed, scripts;

	if ( !context ) {

		// Stop scripts or inline event handlers from being executed immediately
		// by using document.implementation
		if ( support.createHTMLDocument ) {
			context = document.implementation.createHTMLDocument( "" );

			// Set the base href for the created document
			// so any parsed elements with URLs
			// are based on the document's URL (gh-2965)
			base = context.createElement( "base" );
			base.href = document.location.href;
			context.head.appendChild( base );
		} else {
			context = document;
		}
	}

	parsed = rsingleTag.exec( data );
	scripts = !keepScripts && [];

	// Single tag
	if ( parsed ) {
		return [ context.createElement( parsed[ 1 ] ) ];
	}

	parsed = buildFragment( [ data ], context, scripts );

	if ( scripts && scripts.length ) {
		jQuery( scripts ).remove();
	}

	return jQuery.merge( [], parsed.childNodes );
};


/**
 * Load a url into a page
 */
jQuery.fn.load = function( url, params, callback ) {
	var selector, type, response,
		self = this,
		off = url.indexOf( " " );

	if ( off > -1 ) {
		selector = stripAndCollapse( url.slice( off ) );
		url = url.slice( 0, off );
	}

	// If it's a function
	if ( isFunction( params ) ) {

		// We assume that it's the callback
		callback = params;
		params = undefined;

	// Otherwise, build a param string
	} else if ( params && typeof params === "object" ) {
		type = "POST";
	}

	// If we have elements to modify, make the request
	if ( self.length > 0 ) {
		jQuery.ajax( {
			url: url,

			// If "type" variable is undefined, then "GET" method will be used.
			// Make value of this field explicit since
			// user can override it through ajaxSetup method
			type: type || "GET",
			dataType: "html",
			data: params
		} ).done( function( responseText ) {

			// Save response for use in complete callback
			response = arguments;

			self.html( selector ?

				// If a selector was specified, locate the right elements in a dummy div
				// Exclude scripts to avoid IE 'Permission Denied' errors
				jQuery( "<div>" ).append( jQuery.parseHTML( responseText ) ).find( selector ) :

				// Otherwise use the full result
				responseText );

		// If the request succeeds, this function gets "data", "status", "jqXHR"
		// but they are ignored because response was set above.
		// If it fails, this function gets "jqXHR", "status", "error"
		} ).always( callback && function( jqXHR, status ) {
			self.each( function() {
				callback.apply( this, response || [ jqXHR.responseText, status, jqXHR ] );
			} );
		} );
	}

	return this;
};




jQuery.expr.pseudos.animated = function( elem ) {
	return jQuery.grep( jQuery.timers, function( fn ) {
		return elem === fn.elem;
	} ).length;
};




jQuery.offset = {
	setOffset: function( elem, options, i ) {
		var curPosition, curLeft, curCSSTop, curTop, curOffset, curCSSLeft, calculatePosition,
			position = jQuery.css( elem, "position" ),
			curElem = jQuery( elem ),
			props = {};

		// Set position first, in-case top/left are set even on static elem
		if ( position === "static" ) {
			elem.style.position = "relative";
		}

		curOffset = curElem.offset();
		curCSSTop = jQuery.css( elem, "top" );
		curCSSLeft = jQuery.css( elem, "left" );
		calculatePosition = ( position === "absolute" || position === "fixed" ) &&
			( curCSSTop + curCSSLeft ).indexOf( "auto" ) > -1;

		// Need to be able to calculate position if either
		// top or left is auto and position is either absolute or fixed
		if ( calculatePosition ) {
			curPosition = curElem.position();
			curTop = curPosition.top;
			curLeft = curPosition.left;

		} else {
			curTop = parseFloat( curCSSTop ) || 0;
			curLeft = parseFloat( curCSSLeft ) || 0;
		}

		if ( isFunction( options ) ) {

			// Use jQuery.extend here to allow modification of coordinates argument (gh-1848)
			options = options.call( elem, i, jQuery.extend( {}, curOffset ) );
		}

		if ( options.top != null ) {
			props.top = ( options.top - curOffset.top ) + curTop;
		}
		if ( options.left != null ) {
			props.left = ( options.left - curOffset.left ) + curLeft;
		}

		if ( "using" in options ) {
			options.using.call( elem, props );

		} else {
			if ( typeof props.top === "number" ) {
				props.top += "px";
			}
			if ( typeof props.left === "number" ) {
				props.left += "px";
			}
			curElem.css( props );
		}
	}
};

jQuery.fn.extend( {

	// offset() relates an element's border box to the document origin
	offset: function( options ) {

		// Preserve chaining for setter
		if ( arguments.length ) {
			return options === undefined ?
				this :
				this.each( function( i ) {
					jQuery.offset.setOffset( this, options, i );
				} );
		}

		var rect, win,
			elem = this[ 0 ];

		if ( !elem ) {
			return;
		}

		// Return zeros for disconnected and hidden (display: none) elements (gh-2310)
		// Support: IE <=11 only
		// Running getBoundingClientRect on a
		// disconnected node in IE throws an error
		if ( !elem.getClientRects().length ) {
			return { top: 0, left: 0 };
		}

		// Get document-relative position by adding viewport scroll to viewport-relative gBCR
		rect = elem.getBoundingClientRect();
		win = elem.ownerDocument.defaultView;
		return {
			top: rect.top + win.pageYOffset,
			left: rect.left + win.pageXOffset
		};
	},

	// position() relates an element's margin box to its offset parent's padding box
	// This corresponds to the behavior of CSS absolute positioning
	position: function() {
		if ( !this[ 0 ] ) {
			return;
		}

		var offsetParent, offset, doc,
			elem = this[ 0 ],
			parentOffset = { top: 0, left: 0 };

		// position:fixed elements are offset from the viewport, which itself always has zero offset
		if ( jQuery.css( elem, "position" ) === "fixed" ) {

			// Assume position:fixed implies availability of getBoundingClientRect
			offset = elem.getBoundingClientRect();

		} else {
			offset = this.offset();

			// Account for the *real* offset parent, which can be the document or its root element
			// when a statically positioned element is identified
			doc = elem.ownerDocument;
			offsetParent = elem.offsetParent || doc.documentElement;
			while ( offsetParent &&
				( offsetParent === doc.body || offsetParent === doc.documentElement ) &&
				jQuery.css( offsetParent, "position" ) === "static" ) {

				offsetParent = offsetParent.parentNode;
			}
			if ( offsetParent && offsetParent !== elem && offsetParent.nodeType === 1 ) {

				// Incorporate borders into its offset, since they are outside its content origin
				parentOffset = jQuery( offsetParent ).offset();
				parentOffset.top += jQuery.css( offsetParent, "borderTopWidth", true );
				parentOffset.left += jQuery.css( offsetParent, "borderLeftWidth", true );
			}
		}

		// Subtract parent offsets and element margins
		return {
			top: offset.top - parentOffset.top - jQuery.css( elem, "marginTop", true ),
			left: offset.left - parentOffset.left - jQuery.css( elem, "marginLeft", true )
		};
	},

	// This method will return documentElement in the following cases:
	// 1) For the element inside the iframe without offsetParent, this method will return
	//    documentElement of the parent window
	// 2) For the hidden or detached element
	// 3) For body or html element, i.e. in case of the html node - it will return itself
	//
	// but those exceptions were never presented as a real life use-cases
	// and might be considered as more preferable results.
	//
	// This logic, however, is not guaranteed and can change at any point in the future
	offsetParent: function() {
		return this.map( function() {
			var offsetParent = this.offsetParent;

			while ( offsetParent && jQuery.css( offsetParent, "position" ) === "static" ) {
				offsetParent = offsetParent.offsetParent;
			}

			return offsetParent || documentElement;
		} );
	}
} );

// Create scrollLeft and scrollTop methods
jQuery.each( { scrollLeft: "pageXOffset", scrollTop: "pageYOffset" }, function( method, prop ) {
	var top = "pageYOffset" === prop;

	jQuery.fn[ method ] = function( val ) {
		return access( this, function( elem, method, val ) {

			// Coalesce documents and windows
			var win;
			if ( isWindow( elem ) ) {
				win = elem;
			} else if ( elem.nodeType === 9 ) {
				win = elem.defaultView;
			}

			if ( val === undefined ) {
				return win ? win[ prop ] : elem[ method ];
			}

			if ( win ) {
				win.scrollTo(
					!top ? val : win.pageXOffset,
					top ? val : win.pageYOffset
				);

			} else {
				elem[ method ] = val;
			}
		}, method, val, arguments.length );
	};
} );

// Support: Safari <=7 - 9.1, Chrome <=37 - 49
// Add the top/left cssHooks using jQuery.fn.position
// Webkit bug: https://bugs.webkit.org/show_bug.cgi?id=29084
// Blink bug: https://bugs.chromium.org/p/chromium/issues/detail?id=589347
// getComputedStyle returns percent when specified for top/left/bottom/right;
// rather than make the css module depend on the offset module, just check for it here
jQuery.each( [ "top", "left" ], function( _i, prop ) {
	jQuery.cssHooks[ prop ] = addGetHookIf( support.pixelPosition,
		function( elem, computed ) {
			if ( computed ) {
				computed = curCSS( elem, prop );

				// If curCSS returns percentage, fallback to offset
				return rnumnonpx.test( computed ) ?
					jQuery( elem ).position()[ prop ] + "px" :
					computed;
			}
		}
	);
} );


// Create innerHeight, innerWidth, height, width, outerHeight and outerWidth methods
jQuery.each( { Height: "height", Width: "width" }, function( name, type ) {
	jQuery.each( { padding: "inner" + name, content: type, "": "outer" + name },
		function( defaultExtra, funcName ) {

		// Margin is only for outerHeight, outerWidth
		jQuery.fn[ funcName ] = function( margin, value ) {
			var chainable = arguments.length && ( defaultExtra || typeof margin !== "boolean" ),
				extra = defaultExtra || ( margin === true || value === true ? "margin" : "border" );

			return access( this, function( elem, type, value ) {
				var doc;

				if ( isWindow( elem ) ) {

					// $( window ).outerWidth/Height return w/h including scrollbars (gh-1729)
					return funcName.indexOf( "outer" ) === 0 ?
						elem[ "inner" + name ] :
						elem.document.documentElement[ "client" + name ];
				}

				// Get document width or height
				if ( elem.nodeType === 9 ) {
					doc = elem.documentElement;

					// Either scroll[Width/Height] or offset[Width/Height] or client[Width/Height],
					// whichever is greatest
					return Math.max(
						elem.body[ "scroll" + name ], doc[ "scroll" + name ],
						elem.body[ "offset" + name ], doc[ "offset" + name ],
						doc[ "client" + name ]
					);
				}

				return value === undefined ?

					// Get width or height on the element, requesting but not forcing parseFloat
					jQuery.css( elem, type, extra ) :

					// Set width or height on the element
					jQuery.style( elem, type, value, extra );
			}, type, chainable ? margin : undefined, chainable );
		};
	} );
} );


jQuery.each( [
	"ajaxStart",
	"ajaxStop",
	"ajaxComplete",
	"ajaxError",
	"ajaxSuccess",
	"ajaxSend"
], function( _i, type ) {
	jQuery.fn[ type ] = function( fn ) {
		return this.on( type, fn );
	};
} );




jQuery.fn.extend( {

	bind: function( types, data, fn ) {
		return this.on( types, null, data, fn );
	},
	unbind: function( types, fn ) {
		return this.off( types, null, fn );
	},

	delegate: function( selector, types, data, fn ) {
		return this.on( types, selector, data, fn );
	},
	undelegate: function( selector, types, fn ) {

		// ( namespace ) or ( selector, types [, fn] )
		return arguments.length === 1 ?
			this.off( selector, "**" ) :
			this.off( types, selector || "**", fn );
	},

	hover: function( fnOver, fnOut ) {
		return this.mouseenter( fnOver ).mouseleave( fnOut || fnOver );
	}
} );

jQuery.each( ( "blur focus focusin focusout resize scroll click dblclick " +
	"mousedown mouseup mousemove mouseover mouseout mouseenter mouseleave " +
	"change select submit keydown keypress keyup contextmenu" ).split( " " ),
	function( _i, name ) {

		// Handle event binding
		jQuery.fn[ name ] = function( data, fn ) {
			return arguments.length > 0 ?
				this.on( name, null, data, fn ) :
				this.trigger( name );
		};
	} );




// Support: Android <=4.0 only
// Make sure we trim BOM and NBSP
var rtrim = /^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g;

// Bind a function to a context, optionally partially applying any
// arguments.
// jQuery.proxy is deprecated to promote standards (specifically Function#bind)
// However, it is not slated for removal any time soon
jQuery.proxy = function( fn, context ) {
	var tmp, args, proxy;

	if ( typeof context === "string" ) {
		tmp = fn[ context ];
		context = fn;
		fn = tmp;
	}

	// Quick check to determine if target is callable, in the spec
	// this throws a TypeError, but we will just return undefined.
	if ( !isFunction( fn ) ) {
		return undefined;
	}

	// Simulated bind
	args = slice.call( arguments, 2 );
	proxy = function() {
		return fn.apply( context || this, args.concat( slice.call( arguments ) ) );
	};

	// Set the guid of unique handler to the same of original handler, so it can be removed
	proxy.guid = fn.guid = fn.guid || jQuery.guid++;

	return proxy;
};

jQuery.holdReady = function( hold ) {
	if ( hold ) {
		jQuery.readyWait++;
	} else {
		jQuery.ready( true );
	}
};
jQuery.isArray = Array.isArray;
jQuery.parseJSON = JSON.parse;
jQuery.nodeName = nodeName;
jQuery.isFunction = isFunction;
jQuery.isWindow = isWindow;
jQuery.camelCase = camelCase;
jQuery.type = toType;

jQuery.now = Date.now;

jQuery.isNumeric = function( obj ) {

	// As of jQuery 3.0, isNumeric is limited to
	// strings and numbers (primitives or objects)
	// that can be coerced to finite numbers (gh-2662)
	var type = jQuery.type( obj );
	return ( type === "number" || type === "string" ) &&

		// parseFloat NaNs numeric-cast false positives ("")
		// ...but misinterprets leading-number strings, particularly hex literals ("0x...")
		// subtraction forces infinities to NaN
		!isNaN( obj - parseFloat( obj ) );
};

jQuery.trim = function( text ) {
	return text == null ?
		"" :
		( text + "" ).replace( rtrim, "" );
};



// Register as a named AMD module, since jQuery can be concatenated with other
// files that may use define, but not via a proper concatenation script that
// understands anonymous AMD modules. A named AMD is safest and most robust
// way to register. Lowercase jquery is used because AMD module names are
// derived from file names, and jQuery is normally delivered in a lowercase
// file name. Do this after creating the global so that if an AMD module wants
// to call noConflict to hide this version of jQuery, it will work.

// Note that for maximum portability, libraries that are not jQuery should
// declare themselves as anonymous modules, and avoid setting a global if an
// AMD loader is present. jQuery is a special case. For more information, see
// https://github.com/jrburke/requirejs/wiki/Updating-existing-libraries#wiki-anon

if ( typeof define === "function" && define.amd ) {
	define( "jquery", [], function() {
		return jQuery;
	} );
}




var

	// Map over jQuery in case of overwrite
	_jQuery = window.jQuery,

	// Map over the $ in case of overwrite
	_$ = window.$;

jQuery.noConflict = function( deep ) {
	if ( window.$ === jQuery ) {
		window.$ = _$;
	}

	if ( deep && window.jQuery === jQuery ) {
		window.jQuery = _jQuery;
	}

	return jQuery;
};

// Expose jQuery and $ identifiers, even in AMD
// (#7102#comment:10, https://github.com/jquery/jquery/pull/557)
// and CommonJS for browser emulators (#13566)
if ( typeof noGlobal === "undefined" ) {
	window.jQuery = window.$ = jQuery;
}




return jQuery;
} );

/**
 * State-based routing for AngularJS
 * @version v0.4.2
 * @link http://angular-ui.github.com/
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */

/* commonjs package manager support (eg componentjs) */
if (typeof module !== "undefined" && typeof exports !== "undefined" && module.exports === exports){
  module.exports = 'ui.router';
}

(function (window, angular, undefined) {
/*jshint globalstrict:true*/
/*global angular:false*/
'use strict';

var isDefined = angular.isDefined,
    isFunction = angular.isFunction,
    isString = angular.isString,
    isObject = angular.isObject,
    isArray = angular.isArray,
    forEach = angular.forEach,
    extend = angular.extend,
    copy = angular.copy,
    toJson = angular.toJson;

function inherit(parent, extra) {
  return extend(new (extend(function() {}, { prototype: parent }))(), extra);
}

function merge(dst) {
  forEach(arguments, function(obj) {
    if (obj !== dst) {
      forEach(obj, function(value, key) {
        if (!dst.hasOwnProperty(key)) dst[key] = value;
      });
    }
  });
  return dst;
}

/**
 * Finds the common ancestor path between two states.
 *
 * @param {Object} first The first state.
 * @param {Object} second The second state.
 * @return {Array} Returns an array of state names in descending order, not including the root.
 */
function ancestors(first, second) {
  var path = [];

  for (var n in first.path) {
    if (first.path[n] !== second.path[n]) break;
    path.push(first.path[n]);
  }
  return path;
}

/**
 * IE8-safe wrapper for `Object.keys()`.
 *
 * @param {Object} object A JavaScript object.
 * @return {Array} Returns the keys of the object as an array.
 */
function objectKeys(object) {
  if (Object.keys) {
    return Object.keys(object);
  }
  var result = [];

  forEach(object, function(val, key) {
    result.push(key);
  });
  return result;
}

/**
 * IE8-safe wrapper for `Array.prototype.indexOf()`.
 *
 * @param {Array} array A JavaScript array.
 * @param {*} value A value to search the array for.
 * @return {Number} Returns the array index value of `value`, or `-1` if not present.
 */
function indexOf(array, value) {
  if (Array.prototype.indexOf) {
    return array.indexOf(value, Number(arguments[2]) || 0);
  }
  var len = array.length >>> 0, from = Number(arguments[2]) || 0;
  from = (from < 0) ? Math.ceil(from) : Math.floor(from);

  if (from < 0) from += len;

  for (; from < len; from++) {
    if (from in array && array[from] === value) return from;
  }
  return -1;
}

/**
 * Merges a set of parameters with all parameters inherited between the common parents of the
 * current state and a given destination state.
 *
 * @param {Object} currentParams The value of the current state parameters ($stateParams).
 * @param {Object} newParams The set of parameters which will be composited with inherited params.
 * @param {Object} $current Internal definition of object representing the current state.
 * @param {Object} $to Internal definition of object representing state to transition to.
 */
function inheritParams(currentParams, newParams, $current, $to) {
  var parents = ancestors($current, $to), parentParams, inherited = {}, inheritList = [];

  for (var i in parents) {
    if (!parents[i] || !parents[i].params) continue;
    parentParams = objectKeys(parents[i].params);
    if (!parentParams.length) continue;

    for (var j in parentParams) {
      if (indexOf(inheritList, parentParams[j]) >= 0) continue;
      inheritList.push(parentParams[j]);
      inherited[parentParams[j]] = currentParams[parentParams[j]];
    }
  }
  return extend({}, inherited, newParams);
}

/**
 * Performs a non-strict comparison of the subset of two objects, defined by a list of keys.
 *
 * @param {Object} a The first object.
 * @param {Object} b The second object.
 * @param {Array} keys The list of keys within each object to compare. If the list is empty or not specified,
 *                     it defaults to the list of keys in `a`.
 * @return {Boolean} Returns `true` if the keys match, otherwise `false`.
 */
function equalForKeys(a, b, keys) {
  if (!keys) {
    keys = [];
    for (var n in a) keys.push(n); // Used instead of Object.keys() for IE8 compatibility
  }

  for (var i=0; i<keys.length; i++) {
    var k = keys[i];
    if (a[k] != b[k]) return false; // Not '===', values aren't necessarily normalized
  }
  return true;
}

/**
 * Returns the subset of an object, based on a list of keys.
 *
 * @param {Array} keys
 * @param {Object} values
 * @return {Boolean} Returns a subset of `values`.
 */
function filterByKeys(keys, values) {
  var filtered = {};

  forEach(keys, function (name) {
    filtered[name] = values[name];
  });
  return filtered;
}

// like _.indexBy
// when you know that your index values will be unique, or you want last-one-in to win
function indexBy(array, propName) {
  var result = {};
  forEach(array, function(item) {
    result[item[propName]] = item;
  });
  return result;
}

// extracted from underscore.js
// Return a copy of the object only containing the whitelisted properties.
function pick(obj) {
  var copy = {};
  var keys = Array.prototype.concat.apply(Array.prototype, Array.prototype.slice.call(arguments, 1));
  forEach(keys, function(key) {
    if (key in obj) copy[key] = obj[key];
  });
  return copy;
}

// extracted from underscore.js
// Return a copy of the object omitting the blacklisted properties.
function omit(obj) {
  var copy = {};
  var keys = Array.prototype.concat.apply(Array.prototype, Array.prototype.slice.call(arguments, 1));
  for (var key in obj) {
    if (indexOf(keys, key) == -1) copy[key] = obj[key];
  }
  return copy;
}

function pluck(collection, key) {
  var result = isArray(collection) ? [] : {};

  forEach(collection, function(val, i) {
    result[i] = isFunction(key) ? key(val) : val[key];
  });
  return result;
}

function filter(collection, callback) {
  var array = isArray(collection);
  var result = array ? [] : {};
  forEach(collection, function(val, i) {
    if (callback(val, i)) {
      result[array ? result.length : i] = val;
    }
  });
  return result;
}

function map(collection, callback) {
  var result = isArray(collection) ? [] : {};

  forEach(collection, function(val, i) {
    result[i] = callback(val, i);
  });
  return result;
}

// issue #2676 #2889
function silenceUncaughtInPromise (promise) {
  return promise.then(undefined, function() {}) && promise;
}

/**
 * @ngdoc overview
 * @name ui.router.util
 *
 * @description
 * # ui.router.util sub-module
 *
 * This module is a dependency of other sub-modules. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 *
 */
angular.module('ui.router.util', ['ng']);

/**
 * @ngdoc overview
 * @name ui.router.router
 * 
 * @requires ui.router.util
 *
 * @description
 * # ui.router.router sub-module
 *
 * This module is a dependency of other sub-modules. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 */
angular.module('ui.router.router', ['ui.router.util']);

/**
 * @ngdoc overview
 * @name ui.router.state
 * 
 * @requires ui.router.router
 * @requires ui.router.util
 *
 * @description
 * # ui.router.state sub-module
 *
 * This module is a dependency of the main ui.router module. Do not include this module as a dependency
 * in your angular app (use {@link ui.router} module instead).
 * 
 */
angular.module('ui.router.state', ['ui.router.router', 'ui.router.util']);

/**
 * @ngdoc overview
 * @name ui.router
 *
 * @requires ui.router.state
 *
 * @description
 * # ui.router
 * 
 * ## The main module for ui.router 
 * There are several sub-modules included with the ui.router module, however only this module is needed
 * as a dependency within your angular app. The other modules are for organization purposes. 
 *
 * The modules are:
 * * ui.router - the main "umbrella" module
 * * ui.router.router - 
 * 
 * *You'll need to include **only** this module as the dependency within your angular app.*
 * 
 * <pre>
 * <!doctype html>
 * <html ng-app="myApp">
 * <head>
 *   <script src="js/angular.js"></script>
 *   <!-- Include the ui-router script -->
 *   <script src="js/angular-ui-router.min.js"></script>
 *   <script>
 *     // ...and add 'ui.router' as a dependency
 *     var myApp = angular.module('myApp', ['ui.router']);
 *   </script>
 * </head>
 * <body>
 * </body>
 * </html>
 * </pre>
 */
angular.module('ui.router', ['ui.router.state']);

angular.module('ui.router.compat', ['ui.router']);

/**
 * @ngdoc object
 * @name ui.router.util.$resolve
 *
 * @requires $q
 * @requires $injector
 *
 * @description
 * Manages resolution of (acyclic) graphs of promises.
 */
$Resolve.$inject = ['$q', '$injector'];
function $Resolve(  $q,    $injector) {
  
  var VISIT_IN_PROGRESS = 1,
      VISIT_DONE = 2,
      NOTHING = {},
      NO_DEPENDENCIES = [],
      NO_LOCALS = NOTHING,
      NO_PARENT = extend($q.when(NOTHING), { $$promises: NOTHING, $$values: NOTHING });
  

  /**
   * @ngdoc function
   * @name ui.router.util.$resolve#study
   * @methodOf ui.router.util.$resolve
   *
   * @description
   * Studies a set of invocables that are likely to be used multiple times.
   * <pre>
   * $resolve.study(invocables)(locals, parent, self)
   * </pre>
   * is equivalent to
   * <pre>
   * $resolve.resolve(invocables, locals, parent, self)
   * </pre>
   * but the former is more efficient (in fact `resolve` just calls `study` 
   * internally).
   *
   * @param {object} invocables Invocable objects
   * @return {function} a function to pass in locals, parent and self
   */
  this.study = function (invocables) {
    if (!isObject(invocables)) throw new Error("'invocables' must be an object");
    var invocableKeys = objectKeys(invocables || {});
    
    // Perform a topological sort of invocables to build an ordered plan
    var plan = [], cycle = [], visited = {};
    function visit(value, key) {
      if (visited[key] === VISIT_DONE) return;
      
      cycle.push(key);
      if (visited[key] === VISIT_IN_PROGRESS) {
        cycle.splice(0, indexOf(cycle, key));
        throw new Error("Cyclic dependency: " + cycle.join(" -> "));
      }
      visited[key] = VISIT_IN_PROGRESS;
      
      if (isString(value)) {
        plan.push(key, [ function() { return $injector.get(value); }], NO_DEPENDENCIES);
      } else {
        var params = $injector.annotate(value);
        forEach(params, function (param) {
          if (param !== key && invocables.hasOwnProperty(param)) visit(invocables[param], param);
        });
        plan.push(key, value, params);
      }
      
      cycle.pop();
      visited[key] = VISIT_DONE;
    }
    forEach(invocables, visit);
    invocables = cycle = visited = null; // plan is all that's required
    
    function isResolve(value) {
      return isObject(value) && value.then && value.$$promises;
    }
    
    return function (locals, parent, self) {
      if (isResolve(locals) && self === undefined) {
        self = parent; parent = locals; locals = null;
      }
      if (!locals) locals = NO_LOCALS;
      else if (!isObject(locals)) {
        throw new Error("'locals' must be an object");
      }       
      if (!parent) parent = NO_PARENT;
      else if (!isResolve(parent)) {
        throw new Error("'parent' must be a promise returned by $resolve.resolve()");
      }
      
      // To complete the overall resolution, we have to wait for the parent
      // promise and for the promise for each invokable in our plan.
      var resolution = $q.defer(),
          result = silenceUncaughtInPromise(resolution.promise),
          promises = result.$$promises = {},
          values = extend({}, locals),
          wait = 1 + plan.length/3,
          merged = false;

      silenceUncaughtInPromise(result);
          
      function done() {
        // Merge parent values we haven't got yet and publish our own $$values
        if (!--wait) {
          if (!merged) merge(values, parent.$$values); 
          result.$$values = values;
          result.$$promises = result.$$promises || true; // keep for isResolve()
          delete result.$$inheritedValues;
          resolution.resolve(values);
        }
      }
      
      function fail(reason) {
        result.$$failure = reason;
        resolution.reject(reason);
      }

      // Short-circuit if parent has already failed
      if (isDefined(parent.$$failure)) {
        fail(parent.$$failure);
        return result;
      }
      
      if (parent.$$inheritedValues) {
        merge(values, omit(parent.$$inheritedValues, invocableKeys));
      }

      // Merge parent values if the parent has already resolved, or merge
      // parent promises and wait if the parent resolve is still in progress.
      extend(promises, parent.$$promises);
      if (parent.$$values) {
        merged = merge(values, omit(parent.$$values, invocableKeys));
        result.$$inheritedValues = omit(parent.$$values, invocableKeys);
        done();
      } else {
        if (parent.$$inheritedValues) {
          result.$$inheritedValues = omit(parent.$$inheritedValues, invocableKeys);
        }        
        parent.then(done, fail);
      }
      
      // Process each invocable in the plan, but ignore any where a local of the same name exists.
      for (var i=0, ii=plan.length; i<ii; i+=3) {
        if (locals.hasOwnProperty(plan[i])) done();
        else invoke(plan[i], plan[i+1], plan[i+2]);
      }
      
      function invoke(key, invocable, params) {
        // Create a deferred for this invocation. Failures will propagate to the resolution as well.
        var invocation = $q.defer(), waitParams = 0;
        function onfailure(reason) {
          invocation.reject(reason);
          fail(reason);
        }
        // Wait for any parameter that we have a promise for (either from parent or from this
        // resolve; in that case study() will have made sure it's ordered before us in the plan).
        forEach(params, function (dep) {
          if (promises.hasOwnProperty(dep) && !locals.hasOwnProperty(dep)) {
            waitParams++;
            promises[dep].then(function (result) {
              values[dep] = result;
              if (!(--waitParams)) proceed();
            }, onfailure);
          }
        });
        if (!waitParams) proceed();
        function proceed() {
          if (isDefined(result.$$failure)) return;
          try {
            invocation.resolve($injector.invoke(invocable, self, values));
            invocation.promise.then(function (result) {
              values[key] = result;
              done();
            }, onfailure);
          } catch (e) {
            onfailure(e);
          }
        }
        // Publish promise synchronously; invocations further down in the plan may depend on it.
        promises[key] = silenceUncaughtInPromise(invocation.promise);
      }
      
      return result;
    };
  };
  
  /**
   * @ngdoc function
   * @name ui.router.util.$resolve#resolve
   * @methodOf ui.router.util.$resolve
   *
   * @description
   * Resolves a set of invocables. An invocable is a function to be invoked via 
   * `$injector.invoke()`, and can have an arbitrary number of dependencies. 
   * An invocable can either return a value directly,
   * or a `$q` promise. If a promise is returned it will be resolved and the 
   * resulting value will be used instead. Dependencies of invocables are resolved 
   * (in this order of precedence)
   *
   * - from the specified `locals`
   * - from another invocable that is part of this `$resolve` call
   * - from an invocable that is inherited from a `parent` call to `$resolve` 
   *   (or recursively
   * - from any ancestor `$resolve` of that parent).
   *
   * The return value of `$resolve` is a promise for an object that contains 
   * (in this order of precedence)
   *
   * - any `locals` (if specified)
   * - the resolved return values of all injectables
   * - any values inherited from a `parent` call to `$resolve` (if specified)
   *
   * The promise will resolve after the `parent` promise (if any) and all promises 
   * returned by injectables have been resolved. If any invocable 
   * (or `$injector.invoke`) throws an exception, or if a promise returned by an 
   * invocable is rejected, the `$resolve` promise is immediately rejected with the 
   * same error. A rejection of a `parent` promise (if specified) will likewise be 
   * propagated immediately. Once the `$resolve` promise has been rejected, no 
   * further invocables will be called.
   * 
   * Cyclic dependencies between invocables are not permitted and will cause `$resolve`
   * to throw an error. As a special case, an injectable can depend on a parameter 
   * with the same name as the injectable, which will be fulfilled from the `parent` 
   * injectable of the same name. This allows inherited values to be decorated. 
   * Note that in this case any other injectable in the same `$resolve` with the same
   * dependency would see the decorated value, not the inherited value.
   *
   * Note that missing dependencies -- unlike cyclic dependencies -- will cause an 
   * (asynchronous) rejection of the `$resolve` promise rather than a (synchronous) 
   * exception.
   *
   * Invocables are invoked eagerly as soon as all dependencies are available. 
   * This is true even for dependencies inherited from a `parent` call to `$resolve`.
   *
   * As a special case, an invocable can be a string, in which case it is taken to 
   * be a service name to be passed to `$injector.get()`. This is supported primarily 
   * for backwards-compatibility with the `resolve` property of `$routeProvider` 
   * routes.
   *
   * @param {object} invocables functions to invoke or 
   * `$injector` services to fetch.
   * @param {object} locals  values to make available to the injectables
   * @param {object} parent  a promise returned by another call to `$resolve`.
   * @param {object} self  the `this` for the invoked methods
   * @return {object} Promise for an object that contains the resolved return value
   * of all invocables, as well as any inherited and local values.
   */
  this.resolve = function (invocables, locals, parent, self) {
    return this.study(invocables)(locals, parent, self);
  };
}

angular.module('ui.router.util').service('$resolve', $Resolve);



/**
 * @ngdoc object
 * @name ui.router.util.$templateFactoryProvider
 *
 * @description
 * Provider for $templateFactory. Manages which template-loading mechanism to
 * use, and will default to the most recent one ($templateRequest on Angular
 * versions starting from 1.3, $http otherwise).
 */
function TemplateFactoryProvider() {
  var shouldUnsafelyUseHttp = angular.version.minor < 3;

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactoryProvider#shouldUnsafelyUseHttp
   * @methodOf ui.router.util.$templateFactoryProvider
   *
   * @description
   * Forces $templateFactory to use $http instead of $templateRequest. This
   * might cause XSS, as $http doesn't enforce the regular security checks for
   * templates that have been introduced in Angular 1.3. Note that setting this
   * to false on Angular older than 1.3.x will crash, as the $templateRequest
   * service (and the security checks) are not implemented on these versions.
   *
   * See the $sce documentation, section
   * <a href="https://docs.angularjs.org/api/ng/service/$sce#impact-on-loading-templates">
   * Impact on loading templates</a> for more details about this mechanism.
   *
   * @param {boolean} value
   */
  this.shouldUnsafelyUseHttp = function(value) {
    shouldUnsafelyUseHttp = !!value;
  };

  /**
   * @ngdoc object
   * @name ui.router.util.$templateFactory
   *
   * @requires $http
   * @requires $templateCache
   * @requires $injector
   *
   * @description
   * Service. Manages loading of templates.
   */
  this.$get = ['$http', '$templateCache', '$injector', function($http, $templateCache, $injector){
    return new TemplateFactory($http, $templateCache, $injector, shouldUnsafelyUseHttp);}];
}


/**
 * @ngdoc object
 * @name ui.router.util.$templateFactory
 *
 * @requires $http
 * @requires $templateCache
 * @requires $injector
 *
 * @description
 * Service. Manages loading of templates.
 */
function TemplateFactory($http, $templateCache, $injector, shouldUnsafelyUseHttp) {

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromConfig
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template from a configuration object. 
   *
   * @param {object} config Configuration object for which to load a template. 
   * The following properties are search in the specified order, and the first one 
   * that is defined is used to create the template:
   *
   * @param {string|object} config.template html string template or function to 
   * load via {@link ui.router.util.$templateFactory#fromString fromString}.
   * @param {string|object} config.templateUrl url to load or a function returning 
   * the url to load via {@link ui.router.util.$templateFactory#fromUrl fromUrl}.
   * @param {Function} config.templateProvider function to invoke via 
   * {@link ui.router.util.$templateFactory#fromProvider fromProvider}.
   * @param {object} params  Parameters to pass to the template function.
   * @param {object} locals Locals to pass to `invoke` if the template is loaded 
   * via a `templateProvider`. Defaults to `{ params: params }`.
   *
   * @return {string|object}  The template html as a string, or a promise for 
   * that string,or `null` if no template is configured.
   */
  this.fromConfig = function (config, params, locals) {
    return (
      isDefined(config.template) ? this.fromString(config.template, params) :
      isDefined(config.templateUrl) ? this.fromUrl(config.templateUrl, params) :
      isDefined(config.templateProvider) ? this.fromProvider(config.templateProvider, params, locals) :
      null
    );
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromString
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template from a string or a function returning a string.
   *
   * @param {string|object} template html template as a string or function that 
   * returns an html template as a string.
   * @param {object} params Parameters to pass to the template function.
   *
   * @return {string|object} The template html as a string, or a promise for that 
   * string.
   */
  this.fromString = function (template, params) {
    return isFunction(template) ? template(params) : template;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromUrl
   * @methodOf ui.router.util.$templateFactory
   * 
   * @description
   * Loads a template from the a URL via `$http` and `$templateCache`.
   *
   * @param {string|Function} url url of the template to load, or a function 
   * that returns a url.
   * @param {Object} params Parameters to pass to the url function.
   * @return {string|Promise.<string>} The template html as a string, or a promise 
   * for that string.
   */
  this.fromUrl = function (url, params) {
    if (isFunction(url)) url = url(params);
    if (url == null) return null;
    else {
      if(!shouldUnsafelyUseHttp) {
        return $injector.get('$templateRequest')(url);
      } else {
        return $http
          .get(url, { cache: $templateCache, headers: { Accept: 'text/html' }})
          .then(function(response) { return response.data; });
      }
    }
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$templateFactory#fromProvider
   * @methodOf ui.router.util.$templateFactory
   *
   * @description
   * Creates a template by invoking an injectable provider function.
   *
   * @param {Function} provider Function to invoke via `$injector.invoke`
   * @param {Object} params Parameters for the template.
   * @param {Object} locals Locals to pass to `invoke`. Defaults to 
   * `{ params: params }`.
   * @return {string|Promise.<string>} The template html as a string, or a promise 
   * for that string.
   */
  this.fromProvider = function (provider, params, locals) {
    return $injector.invoke(provider, null, locals || { params: params });
  };
}

angular.module('ui.router.util').provider('$templateFactory', TemplateFactoryProvider);

var $$UMFP; // reference to $UrlMatcherFactoryProvider

/**
 * @ngdoc object
 * @name ui.router.util.type:UrlMatcher
 *
 * @description
 * Matches URLs against patterns and extracts named parameters from the path or the search
 * part of the URL. A URL pattern consists of a path pattern, optionally followed by '?' and a list
 * of search parameters. Multiple search parameter names are separated by '&'. Search parameters
 * do not influence whether or not a URL is matched, but their values are passed through into
 * the matched parameters returned by {@link ui.router.util.type:UrlMatcher#methods_exec exec}.
 *
 * Path parameter placeholders can be specified using simple colon/catch-all syntax or curly brace
 * syntax, which optionally allows a regular expression for the parameter to be specified:
 *
 * * `':'` name - colon placeholder
 * * `'*'` name - catch-all placeholder
 * * `'{' name '}'` - curly placeholder
 * * `'{' name ':' regexp|type '}'` - curly placeholder with regexp or type name. Should the
 *   regexp itself contain curly braces, they must be in matched pairs or escaped with a backslash.
 *
 * Parameter names may contain only word characters (latin letters, digits, and underscore) and
 * must be unique within the pattern (across both path and search parameters). For colon
 * placeholders or curly placeholders without an explicit regexp, a path parameter matches any
 * number of characters other than '/'. For catch-all placeholders the path parameter matches
 * any number of characters.
 *
 * Examples:
 *
 * * `'/hello/'` - Matches only if the path is exactly '/hello/'. There is no special treatment for
 *   trailing slashes, and patterns have to match the entire path, not just a prefix.
 * * `'/user/:id'` - Matches '/user/bob' or '/user/1234!!!' or even '/user/' but not '/user' or
 *   '/user/bob/details'. The second path segment will be captured as the parameter 'id'.
 * * `'/user/{id}'` - Same as the previous example, but using curly brace syntax.
 * * `'/user/{id:[^/]*}'` - Same as the previous example.
 * * `'/user/{id:[0-9a-fA-F]{1,8}}'` - Similar to the previous example, but only matches if the id
 *   parameter consists of 1 to 8 hex digits.
 * * `'/files/{path:.*}'` - Matches any URL starting with '/files/' and captures the rest of the
 *   path into the parameter 'path'.
 * * `'/files/*path'` - ditto.
 * * `'/calendar/{start:date}'` - Matches "/calendar/2014-11-12" (because the pattern defined
 *   in the built-in  `date` Type matches `2014-11-12`) and provides a Date object in $stateParams.start
 *
 * @param {string} pattern  The pattern to compile into a matcher.
 * @param {Object} config  A configuration object hash:
 * @param {Object=} parentMatcher Used to concatenate the pattern/config onto
 *   an existing UrlMatcher
 *
 * * `caseInsensitive` - `true` if URL matching should be case insensitive, otherwise `false`, the default value (for backward compatibility) is `false`.
 * * `strict` - `false` if matching against a URL with a trailing slash should be treated as equivalent to a URL without a trailing slash, the default value is `true`.
 *
 * @property {string} prefix  A static prefix of this pattern. The matcher guarantees that any
 *   URL matching this matcher (i.e. any string for which {@link ui.router.util.type:UrlMatcher#methods_exec exec()} returns
 *   non-null) will start with this prefix.
 *
 * @property {string} source  The pattern that was passed into the constructor
 *
 * @property {string} sourcePath  The path portion of the source property
 *
 * @property {string} sourceSearch  The search portion of the source property
 *
 * @property {string} regex  The constructed regex that will be used to match against the url when
 *   it is time to determine which url will match.
 *
 * @returns {Object}  New `UrlMatcher` object
 */
function UrlMatcher(pattern, config, parentMatcher) {
  config = extend({ params: {} }, isObject(config) ? config : {});

  // Find all placeholders and create a compiled pattern, using either classic or curly syntax:
  //   '*' name
  //   ':' name
  //   '{' name '}'
  //   '{' name ':' regexp '}'
  // The regular expression is somewhat complicated due to the need to allow curly braces
  // inside the regular expression. The placeholder regexp breaks down as follows:
  //    ([:*])([\w\[\]]+)              - classic placeholder ($1 / $2) (search version has - for snake-case)
  //    \{([\w\[\]]+)(?:\:\s*( ... ))?\}  - curly brace placeholder ($3) with optional regexp/type ... ($4) (search version has - for snake-case
  //    (?: ... | ... | ... )+         - the regexp consists of any number of atoms, an atom being either
  //    [^{}\\]+                       - anything other than curly braces or backslash
  //    \\.                            - a backslash escape
  //    \{(?:[^{}\\]+|\\.)*\}          - a matched set of curly braces containing other atoms
  var placeholder       = /([:*])([\w\[\]]+)|\{([\w\[\]]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g,
      searchPlaceholder = /([:]?)([\w\[\].-]+)|\{([\w\[\].-]+)(?:\:\s*((?:[^{}\\]+|\\.|\{(?:[^{}\\]+|\\.)*\})+))?\}/g,
      compiled = '^', last = 0, m,
      segments = this.segments = [],
      parentParams = parentMatcher ? parentMatcher.params : {},
      params = this.params = parentMatcher ? parentMatcher.params.$$new() : new $$UMFP.ParamSet(),
      paramNames = [];

  function addParameter(id, type, config, location) {
    paramNames.push(id);
    if (parentParams[id]) return parentParams[id];
    if (!/^\w+([-.]+\w+)*(?:\[\])?$/.test(id)) throw new Error("Invalid parameter name '" + id + "' in pattern '" + pattern + "'");
    if (params[id]) throw new Error("Duplicate parameter name '" + id + "' in pattern '" + pattern + "'");
    params[id] = new $$UMFP.Param(id, type, config, location);
    return params[id];
  }

  function quoteRegExp(string, pattern, squash, optional) {
    var surroundPattern = ['',''], result = string.replace(/[\\\[\]\^$*+?.()|{}]/g, "\\$&");
    if (!pattern) return result;
    switch(squash) {
      case false: surroundPattern = ['(', ')' + (optional ? "?" : "")]; break;
      case true:
        result = result.replace(/\/$/, '');
        surroundPattern = ['(?:\/(', ')|\/)?'];
      break;
      default:    surroundPattern = ['(' + squash + "|", ')?']; break;
    }
    return result + surroundPattern[0] + pattern + surroundPattern[1];
  }

  this.source = pattern;

  // Split into static segments separated by path parameter placeholders.
  // The number of segments is always 1 more than the number of parameters.
  function matchDetails(m, isSearch) {
    var id, regexp, segment, type, cfg, arrayMode;
    id          = m[2] || m[3]; // IE[78] returns '' for unmatched groups instead of null
    cfg         = config.params[id];
    segment     = pattern.substring(last, m.index);
    regexp      = isSearch ? m[4] : m[4] || (m[1] == '*' ? '.*' : null);

    if (regexp) {
      type      = $$UMFP.type(regexp) || inherit($$UMFP.type("string"), { pattern: new RegExp(regexp, config.caseInsensitive ? 'i' : undefined) });
    }

    return {
      id: id, regexp: regexp, segment: segment, type: type, cfg: cfg
    };
  }

  var p, param, segment;
  while ((m = placeholder.exec(pattern))) {
    p = matchDetails(m, false);
    if (p.segment.indexOf('?') >= 0) break; // we're into the search part

    param = addParameter(p.id, p.type, p.cfg, "path");
    compiled += quoteRegExp(p.segment, param.type.pattern.source, param.squash, param.isOptional);
    segments.push(p.segment);
    last = placeholder.lastIndex;
  }
  segment = pattern.substring(last);

  // Find any search parameter names and remove them from the last segment
  var i = segment.indexOf('?');

  if (i >= 0) {
    var search = this.sourceSearch = segment.substring(i);
    segment = segment.substring(0, i);
    this.sourcePath = pattern.substring(0, last + i);

    if (search.length > 0) {
      last = 0;
      while ((m = searchPlaceholder.exec(search))) {
        p = matchDetails(m, true);
        param = addParameter(p.id, p.type, p.cfg, "search");
        last = placeholder.lastIndex;
        // check if ?&
      }
    }
  } else {
    this.sourcePath = pattern;
    this.sourceSearch = '';
  }

  compiled += quoteRegExp(segment) + (config.strict === false ? '\/?' : '') + '$';
  segments.push(segment);

  this.regexp = new RegExp(compiled, config.caseInsensitive ? 'i' : undefined);
  this.prefix = segments[0];
  this.$$paramNames = paramNames;
}

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#concat
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Returns a new matcher for a pattern constructed by appending the path part and adding the
 * search parameters of the specified pattern to this pattern. The current pattern is not
 * modified. This can be understood as creating a pattern for URLs that are relative to (or
 * suffixes of) the current pattern.
 *
 * @example
 * The following two matchers are equivalent:
 * <pre>
 * new UrlMatcher('/user/{id}?q').concat('/details?date');
 * new UrlMatcher('/user/{id}/details?q&date');
 * </pre>
 *
 * @param {string} pattern  The pattern to append.
 * @param {Object} config  An object hash of the configuration for the matcher.
 * @returns {UrlMatcher}  A matcher for the concatenated pattern.
 */
UrlMatcher.prototype.concat = function (pattern, config) {
  // Because order of search parameters is irrelevant, we can add our own search
  // parameters to the end of the new pattern. Parse the new pattern by itself
  // and then join the bits together, but it's much easier to do this on a string level.
  var defaultConfig = {
    caseInsensitive: $$UMFP.caseInsensitive(),
    strict: $$UMFP.strictMode(),
    squash: $$UMFP.defaultSquashPolicy()
  };
  return new UrlMatcher(this.sourcePath + pattern + this.sourceSearch, extend(defaultConfig, config), this);
};

UrlMatcher.prototype.toString = function () {
  return this.source;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#exec
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Tests the specified path against this matcher, and returns an object containing the captured
 * parameter values, or null if the path does not match. The returned object contains the values
 * of any search parameters that are mentioned in the pattern, but their value may be null if
 * they are not present in `searchParams`. This means that search parameters are always treated
 * as optional.
 *
 * @example
 * <pre>
 * new UrlMatcher('/user/{id}?q&r').exec('/user/bob', {
 *   x: '1', q: 'hello'
 * });
 * // returns { id: 'bob', q: 'hello', r: null }
 * </pre>
 *
 * @param {string} path  The URL path to match, e.g. `$location.path()`.
 * @param {Object} searchParams  URL search parameters, e.g. `$location.search()`.
 * @returns {Object}  The captured parameter values.
 */
UrlMatcher.prototype.exec = function (path, searchParams) {
  var m = this.regexp.exec(path);
  if (!m) return null;
  searchParams = searchParams || {};

  var paramNames = this.parameters(), nTotal = paramNames.length,
    nPath = this.segments.length - 1,
    values = {}, i, j, cfg, paramName;

  if (nPath !== m.length - 1) throw new Error("Unbalanced capture group in route '" + this.source + "'");

  function decodePathArray(string) {
    function reverseString(str) { return str.split("").reverse().join(""); }
    function unquoteDashes(str) { return str.replace(/\\-/g, "-"); }

    var split = reverseString(string).split(/-(?!\\)/);
    var allReversed = map(split, reverseString);
    return map(allReversed, unquoteDashes).reverse();
  }

  var param, paramVal;
  for (i = 0; i < nPath; i++) {
    paramName = paramNames[i];
    param = this.params[paramName];
    paramVal = m[i+1];
    // if the param value matches a pre-replace pair, replace the value before decoding.
    for (j = 0; j < param.replace.length; j++) {
      if (param.replace[j].from === paramVal) paramVal = param.replace[j].to;
    }
    if (paramVal && param.array === true) paramVal = decodePathArray(paramVal);
    if (isDefined(paramVal)) paramVal = param.type.decode(paramVal);
    values[paramName] = param.value(paramVal);
  }
  for (/**/; i < nTotal; i++) {
    paramName = paramNames[i];
    values[paramName] = this.params[paramName].value(searchParams[paramName]);
    param = this.params[paramName];
    paramVal = searchParams[paramName];
    for (j = 0; j < param.replace.length; j++) {
      if (param.replace[j].from === paramVal) paramVal = param.replace[j].to;
    }
    if (isDefined(paramVal)) paramVal = param.type.decode(paramVal);
    values[paramName] = param.value(paramVal);
  }

  return values;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#parameters
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Returns the names of all path and search parameters of this pattern in an unspecified order.
 *
 * @returns {Array.<string>}  An array of parameter names. Must be treated as read-only. If the
 *    pattern has no parameters, an empty array is returned.
 */
UrlMatcher.prototype.parameters = function (param) {
  if (!isDefined(param)) return this.$$paramNames;
  return this.params[param] || null;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#validates
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Checks an object hash of parameters to validate their correctness according to the parameter
 * types of this `UrlMatcher`.
 *
 * @param {Object} params The object hash of parameters to validate.
 * @returns {boolean} Returns `true` if `params` validates, otherwise `false`.
 */
UrlMatcher.prototype.validates = function (params) {
  return this.params.$$validates(params);
};

/**
 * @ngdoc function
 * @name ui.router.util.type:UrlMatcher#format
 * @methodOf ui.router.util.type:UrlMatcher
 *
 * @description
 * Creates a URL that matches this pattern by substituting the specified values
 * for the path and search parameters. Null values for path parameters are
 * treated as empty strings.
 *
 * @example
 * <pre>
 * new UrlMatcher('/user/{id}?q').format({ id:'bob', q:'yes' });
 * // returns '/user/bob?q=yes'
 * </pre>
 *
 * @param {Object} values  the values to substitute for the parameters in this pattern.
 * @returns {string}  the formatted URL (path and optionally search part).
 */
UrlMatcher.prototype.format = function (values) {
  values = values || {};
  var segments = this.segments, params = this.parameters(), paramset = this.params;
  if (!this.validates(values)) return null;

  var i, search = false, nPath = segments.length - 1, nTotal = params.length, result = segments[0];

  function encodeDashes(str) { // Replace dashes with encoded "\-"
    return encodeURIComponent(str).replace(/-/g, function(c) { return '%5C%' + c.charCodeAt(0).toString(16).toUpperCase(); });
  }

  for (i = 0; i < nTotal; i++) {
    var isPathParam = i < nPath;
    var name = params[i], param = paramset[name], value = param.value(values[name]);
    var isDefaultValue = param.isOptional && param.type.equals(param.value(), value);
    var squash = isDefaultValue ? param.squash : false;
    var encoded = param.type.encode(value);

    if (isPathParam) {
      var nextSegment = segments[i + 1];
      var isFinalPathParam = i + 1 === nPath;

      if (squash === false) {
        if (encoded != null) {
          if (isArray(encoded)) {
            result += map(encoded, encodeDashes).join("-");
          } else {
            result += encodeURIComponent(encoded);
          }
        }
        result += nextSegment;
      } else if (squash === true) {
        var capture = result.match(/\/$/) ? /\/?(.*)/ : /(.*)/;
        result += nextSegment.match(capture)[1];
      } else if (isString(squash)) {
        result += squash + nextSegment;
      }

      if (isFinalPathParam && param.squash === true && result.slice(-1) === '/') result = result.slice(0, -1);
    } else {
      if (encoded == null || (isDefaultValue && squash !== false)) continue;
      if (!isArray(encoded)) encoded = [ encoded ];
      if (encoded.length === 0) continue;
      encoded = map(encoded, encodeURIComponent).join('&' + name + '=');
      result += (search ? '&' : '?') + (name + '=' + encoded);
      search = true;
    }
  }

  return result;
};

/**
 * @ngdoc object
 * @name ui.router.util.type:Type
 *
 * @description
 * Implements an interface to define custom parameter types that can be decoded from and encoded to
 * string parameters matched in a URL. Used by {@link ui.router.util.type:UrlMatcher `UrlMatcher`}
 * objects when matching or formatting URLs, or comparing or validating parameter values.
 *
 * See {@link ui.router.util.$urlMatcherFactory#methods_type `$urlMatcherFactory#type()`} for more
 * information on registering custom types.
 *
 * @param {Object} config  A configuration object which contains the custom type definition.  The object's
 *        properties will override the default methods and/or pattern in `Type`'s public interface.
 * @example
 * <pre>
 * {
 *   decode: function(val) { return parseInt(val, 10); },
 *   encode: function(val) { return val && val.toString(); },
 *   equals: function(a, b) { return this.is(a) && a === b; },
 *   is: function(val) { return angular.isNumber(val) isFinite(val) && val % 1 === 0; },
 *   pattern: /\d+/
 * }
 * </pre>
 *
 * @property {RegExp} pattern The regular expression pattern used to match values of this type when
 *           coming from a substring of a URL.
 *
 * @returns {Object}  Returns a new `Type` object.
 */
function Type(config) {
  extend(this, config);
}

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#is
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Detects whether a value is of a particular type. Accepts a native (decoded) value
 * and determines whether it matches the current `Type` object.
 *
 * @param {*} val  The value to check.
 * @param {string} key  Optional. If the type check is happening in the context of a specific
 *        {@link ui.router.util.type:UrlMatcher `UrlMatcher`} object, this is the name of the
 *        parameter in which `val` is stored. Can be used for meta-programming of `Type` objects.
 * @returns {Boolean}  Returns `true` if the value matches the type, otherwise `false`.
 */
Type.prototype.is = function(val, key) {
  return true;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#encode
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Encodes a custom/native type value to a string that can be embedded in a URL. Note that the
 * return value does *not* need to be URL-safe (i.e. passed through `encodeURIComponent()`), it
 * only needs to be a representation of `val` that has been coerced to a string.
 *
 * @param {*} val  The value to encode.
 * @param {string} key  The name of the parameter in which `val` is stored. Can be used for
 *        meta-programming of `Type` objects.
 * @returns {string}  Returns a string representation of `val` that can be encoded in a URL.
 */
Type.prototype.encode = function(val, key) {
  return val;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#decode
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Converts a parameter value (from URL string or transition param) to a custom/native value.
 *
 * @param {string} val  The URL parameter value to decode.
 * @param {string} key  The name of the parameter in which `val` is stored. Can be used for
 *        meta-programming of `Type` objects.
 * @returns {*}  Returns a custom representation of the URL parameter value.
 */
Type.prototype.decode = function(val, key) {
  return val;
};

/**
 * @ngdoc function
 * @name ui.router.util.type:Type#equals
 * @methodOf ui.router.util.type:Type
 *
 * @description
 * Determines whether two decoded values are equivalent.
 *
 * @param {*} a  A value to compare against.
 * @param {*} b  A value to compare against.
 * @returns {Boolean}  Returns `true` if the values are equivalent/equal, otherwise `false`.
 */
Type.prototype.equals = function(a, b) {
  return a == b;
};

Type.prototype.$subPattern = function() {
  var sub = this.pattern.toString();
  return sub.substr(1, sub.length - 2);
};

Type.prototype.pattern = /.*/;

Type.prototype.toString = function() { return "{Type:" + this.name + "}"; };

/** Given an encoded string, or a decoded object, returns a decoded object */
Type.prototype.$normalize = function(val) {
  return this.is(val) ? val : this.decode(val);
};

/*
 * Wraps an existing custom Type as an array of Type, depending on 'mode'.
 * e.g.:
 * - urlmatcher pattern "/path?{queryParam[]:int}"
 * - url: "/path?queryParam=1&queryParam=2
 * - $stateParams.queryParam will be [1, 2]
 * if `mode` is "auto", then
 * - url: "/path?queryParam=1 will create $stateParams.queryParam: 1
 * - url: "/path?queryParam=1&queryParam=2 will create $stateParams.queryParam: [1, 2]
 */
Type.prototype.$asArray = function(mode, isSearch) {
  if (!mode) return this;
  if (mode === "auto" && !isSearch) throw new Error("'auto' array mode is for query parameters only");

  function ArrayType(type, mode) {
    function bindTo(type, callbackName) {
      return function() {
        return type[callbackName].apply(type, arguments);
      };
    }

    // Wrap non-array value as array
    function arrayWrap(val) { return isArray(val) ? val : (isDefined(val) ? [ val ] : []); }
    // Unwrap array value for "auto" mode. Return undefined for empty array.
    function arrayUnwrap(val) {
      switch(val.length) {
        case 0: return undefined;
        case 1: return mode === "auto" ? val[0] : val;
        default: return val;
      }
    }
    function falsey(val) { return !val; }

    // Wraps type (.is/.encode/.decode) functions to operate on each value of an array
    function arrayHandler(callback, allTruthyMode) {
      return function handleArray(val) {
        if (isArray(val) && val.length === 0) return val;
        val = arrayWrap(val);
        var result = map(val, callback);
        if (allTruthyMode === true)
          return filter(result, falsey).length === 0;
        return arrayUnwrap(result);
      };
    }

    // Wraps type (.equals) functions to operate on each value of an array
    function arrayEqualsHandler(callback) {
      return function handleArray(val1, val2) {
        var left = arrayWrap(val1), right = arrayWrap(val2);
        if (left.length !== right.length) return false;
        for (var i = 0; i < left.length; i++) {
          if (!callback(left[i], right[i])) return false;
        }
        return true;
      };
    }

    this.encode = arrayHandler(bindTo(type, 'encode'));
    this.decode = arrayHandler(bindTo(type, 'decode'));
    this.is     = arrayHandler(bindTo(type, 'is'), true);
    this.equals = arrayEqualsHandler(bindTo(type, 'equals'));
    this.pattern = type.pattern;
    this.$normalize = arrayHandler(bindTo(type, '$normalize'));
    this.name = type.name;
    this.$arrayMode = mode;
  }

  return new ArrayType(this, mode);
};



/**
 * @ngdoc object
 * @name ui.router.util.$urlMatcherFactory
 *
 * @description
 * Factory for {@link ui.router.util.type:UrlMatcher `UrlMatcher`} instances. The factory
 * is also available to providers under the name `$urlMatcherFactoryProvider`.
 */
function $UrlMatcherFactory() {
  $$UMFP = this;

  var isCaseInsensitive = false, isStrictMode = true, defaultSquashPolicy = false;

  // Use tildes to pre-encode slashes.
  // If the slashes are simply URLEncoded, the browser can choose to pre-decode them,
  // and bidirectional encoding/decoding fails.
  // Tilde was chosen because it's not a RFC 3986 section 2.2 Reserved Character
  function valToString(val) { return val != null ? val.toString().replace(/(~|\/)/g, function (m) { return {'~':'~~', '/':'~2F'}[m]; }) : val; }
  function valFromString(val) { return val != null ? val.toString().replace(/(~~|~2F)/g, function (m) { return {'~~':'~', '~2F':'/'}[m]; }) : val; }

  var $types = {}, enqueue = true, typeQueue = [], injector, defaultTypes = {
    "string": {
      encode: valToString,
      decode: valFromString,
      // TODO: in 1.0, make string .is() return false if value is undefined/null by default.
      // In 0.2.x, string params are optional by default for backwards compat
      is: function(val) { return val == null || !isDefined(val) || typeof val === "string"; },
      pattern: /[^/]*/
    },
    "int": {
      encode: valToString,
      decode: function(val) { return parseInt(val, 10); },
      is: function(val) { return val !== undefined && val !== null && this.decode(val.toString()) === val; },
      pattern: /\d+/
    },
    "bool": {
      encode: function(val) { return val ? 1 : 0; },
      decode: function(val) { return parseInt(val, 10) !== 0; },
      is: function(val) { return val === true || val === false; },
      pattern: /0|1/
    },
    "date": {
      encode: function (val) {
        if (!this.is(val))
          return undefined;
        return [ val.getFullYear(),
          ('0' + (val.getMonth() + 1)).slice(-2),
          ('0' + val.getDate()).slice(-2)
        ].join("-");
      },
      decode: function (val) {
        if (this.is(val)) return val;
        var match = this.capture.exec(val);
        return match ? new Date(match[1], match[2] - 1, match[3]) : undefined;
      },
      is: function(val) { return val instanceof Date && !isNaN(val.valueOf()); },
      equals: function (a, b) { return this.is(a) && this.is(b) && a.toISOString() === b.toISOString(); },
      pattern: /[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[1-2][0-9]|3[0-1])/,
      capture: /([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/
    },
    "json": {
      encode: angular.toJson,
      decode: angular.fromJson,
      is: angular.isObject,
      equals: angular.equals,
      pattern: /[^/]*/
    },
    "any": { // does not encode/decode
      encode: angular.identity,
      decode: angular.identity,
      equals: angular.equals,
      pattern: /.*/
    }
  };

  function getDefaultConfig() {
    return {
      strict: isStrictMode,
      caseInsensitive: isCaseInsensitive
    };
  }

  function isInjectable(value) {
    return (isFunction(value) || (isArray(value) && isFunction(value[value.length - 1])));
  }

  /**
   * [Internal] Get the default value of a parameter, which may be an injectable function.
   */
  $UrlMatcherFactory.$$getDefaultValue = function(config) {
    if (!isInjectable(config.value)) return config.value;
    if (!injector) throw new Error("Injectable functions cannot be called at configuration time");
    return injector.invoke(config.value);
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#caseInsensitive
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Defines whether URL matching should be case sensitive (the default behavior), or not.
   *
   * @param {boolean} value `false` to match URL in a case sensitive manner; otherwise `true`;
   * @returns {boolean} the current value of caseInsensitive
   */
  this.caseInsensitive = function(value) {
    if (isDefined(value))
      isCaseInsensitive = value;
    return isCaseInsensitive;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#strictMode
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Defines whether URLs should match trailing slashes, or not (the default behavior).
   *
   * @param {boolean=} value `false` to match trailing slashes in URLs, otherwise `true`.
   * @returns {boolean} the current value of strictMode
   */
  this.strictMode = function(value) {
    if (isDefined(value))
      isStrictMode = value;
    return isStrictMode;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#defaultSquashPolicy
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Sets the default behavior when generating or matching URLs with default parameter values.
   *
   * @param {string} value A string that defines the default parameter URL squashing behavior.
   *    `nosquash`: When generating an href with a default parameter value, do not squash the parameter value from the URL
   *    `slash`: When generating an href with a default parameter value, squash (remove) the parameter value, and, if the
   *             parameter is surrounded by slashes, squash (remove) one slash from the URL
   *    any other string, e.g. "~": When generating an href with a default parameter value, squash (remove)
   *             the parameter value from the URL and replace it with this string.
   */
  this.defaultSquashPolicy = function(value) {
    if (!isDefined(value)) return defaultSquashPolicy;
    if (value !== true && value !== false && !isString(value))
      throw new Error("Invalid squash policy: " + value + ". Valid policies: false, true, arbitrary-string");
    defaultSquashPolicy = value;
    return value;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#compile
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Creates a {@link ui.router.util.type:UrlMatcher `UrlMatcher`} for the specified pattern.
   *
   * @param {string} pattern  The URL pattern.
   * @param {Object} config  The config object hash.
   * @returns {UrlMatcher}  The UrlMatcher.
   */
  this.compile = function (pattern, config) {
    return new UrlMatcher(pattern, extend(getDefaultConfig(), config));
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#isMatcher
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Returns true if the specified object is a `UrlMatcher`, or false otherwise.
   *
   * @param {Object} object  The object to perform the type check against.
   * @returns {Boolean}  Returns `true` if the object matches the `UrlMatcher` interface, by
   *          implementing all the same methods.
   */
  this.isMatcher = function (o) {
    if (!isObject(o)) return false;
    var result = true;

    forEach(UrlMatcher.prototype, function(val, name) {
      if (isFunction(val)) {
        result = result && (isDefined(o[name]) && isFunction(o[name]));
      }
    });
    return result;
  };

  /**
   * @ngdoc function
   * @name ui.router.util.$urlMatcherFactory#type
   * @methodOf ui.router.util.$urlMatcherFactory
   *
   * @description
   * Registers a custom {@link ui.router.util.type:Type `Type`} object that can be used to
   * generate URLs with typed parameters.
   *
   * @param {string} name  The type name.
   * @param {Object|Function} definition   The type definition. See
   *        {@link ui.router.util.type:Type `Type`} for information on the values accepted.
   * @param {Object|Function} definitionFn (optional) A function that is injected before the app
   *        runtime starts.  The result of this function is merged into the existing `definition`.
   *        See {@link ui.router.util.type:Type `Type`} for information on the values accepted.
   *
   * @returns {Object}  Returns `$urlMatcherFactoryProvider`.
   *
   * @example
   * This is a simple example of a custom type that encodes and decodes items from an
   * array, using the array index as the URL-encoded value:
   *
   * <pre>
   * var list = ['John', 'Paul', 'George', 'Ringo'];
   *
   * $urlMatcherFactoryProvider.type('listItem', {
   *   encode: function(item) {
   *     // Represent the list item in the URL using its corresponding index
   *     return list.indexOf(item);
   *   },
   *   decode: function(item) {
   *     // Look up the list item by index
   *     return list[parseInt(item, 10)];
   *   },
   *   is: function(item) {
   *     // Ensure the item is valid by checking to see that it appears
   *     // in the list
   *     return list.indexOf(item) > -1;
   *   }
   * });
   *
   * $stateProvider.state('list', {
   *   url: "/list/{item:listItem}",
   *   controller: function($scope, $stateParams) {
   *     console.log($stateParams.item);
   *   }
   * });
   *
   * // ...
   *
   * // Changes URL to '/list/3', logs "Ringo" to the console
   * $state.go('list', { item: "Ringo" });
   * </pre>
   *
   * This is a more complex example of a type that relies on dependency injection to
   * interact with services, and uses the parameter name from the URL to infer how to
   * handle encoding and decoding parameter values:
   *
   * <pre>
   * // Defines a custom type that gets a value from a service,
   * // where each service gets different types of values from
   * // a backend API:
   * $urlMatcherFactoryProvider.type('dbObject', {}, function(Users, Posts) {
   *
   *   // Matches up services to URL parameter names
   *   var services = {
   *     user: Users,
   *     post: Posts
   *   };
   *
   *   return {
   *     encode: function(object) {
   *       // Represent the object in the URL using its unique ID
   *       return object.id;
   *     },
   *     decode: function(value, key) {
   *       // Look up the object by ID, using the parameter
   *       // name (key) to call the correct service
   *       return services[key].findById(value);
   *     },
   *     is: function(object, key) {
   *       // Check that object is a valid dbObject
   *       return angular.isObject(object) && object.id && services[key];
   *     }
   *     equals: function(a, b) {
   *       // Check the equality of decoded objects by comparing
   *       // their unique IDs
   *       return a.id === b.id;
   *     }
   *   };
   * });
   *
   * // In a config() block, you can then attach URLs with
   * // type-annotated parameters:
   * $stateProvider.state('users', {
   *   url: "/users",
   *   // ...
   * }).state('users.item', {
   *   url: "/{user:dbObject}",
   *   controller: function($scope, $stateParams) {
   *     // $stateParams.user will now be an object returned from
   *     // the Users service
   *   },
   *   // ...
   * });
   * </pre>
   */
  this.type = function (name, definition, definitionFn) {
    if (!isDefined(definition)) return $types[name];
    if ($types.hasOwnProperty(name)) throw new Error("A type named '" + name + "' has already been defined.");

    $types[name] = new Type(extend({ name: name }, definition));
    if (definitionFn) {
      typeQueue.push({ name: name, def: definitionFn });
      if (!enqueue) flushTypeQueue();
    }
    return this;
  };

  // `flushTypeQueue()` waits until `$urlMatcherFactory` is injected before invoking the queued `definitionFn`s
  function flushTypeQueue() {
    while(typeQueue.length) {
      var type = typeQueue.shift();
      if (type.pattern) throw new Error("You cannot override a type's .pattern at runtime.");
      angular.extend($types[type.name], injector.invoke(type.def));
    }
  }

  // Register default types. Store them in the prototype of $types.
  forEach(defaultTypes, function(type, name) { $types[name] = new Type(extend({name: name}, type)); });
  $types = inherit($types, {});

  /* No need to document $get, since it returns this */
  this.$get = ['$injector', function ($injector) {
    injector = $injector;
    enqueue = false;
    flushTypeQueue();

    forEach(defaultTypes, function(type, name) {
      if (!$types[name]) $types[name] = new Type(type);
    });
    return this;
  }];

  this.Param = function Param(id, type, config, location) {
    var self = this;
    config = unwrapShorthand(config);
    type = getType(config, type, location);
    var arrayMode = getArrayMode();
    type = arrayMode ? type.$asArray(arrayMode, location === "search") : type;
    if (type.name === "string" && !arrayMode && location === "path" && config.value === undefined)
      config.value = ""; // for 0.2.x; in 0.3.0+ do not automatically default to ""
    var isOptional = config.value !== undefined;
    var squash = getSquashPolicy(config, isOptional);
    var replace = getReplace(config, arrayMode, isOptional, squash);

    function unwrapShorthand(config) {
      var keys = isObject(config) ? objectKeys(config) : [];
      var isShorthand = indexOf(keys, "value") === -1 && indexOf(keys, "type") === -1 &&
                        indexOf(keys, "squash") === -1 && indexOf(keys, "array") === -1;
      if (isShorthand) config = { value: config };
      config.$$fn = isInjectable(config.value) ? config.value : function () { return config.value; };
      return config;
    }

    function getType(config, urlType, location) {
      if (config.type && urlType) throw new Error("Param '"+id+"' has two type configurations.");
      if (urlType) return urlType;
      if (!config.type) return (location === "config" ? $types.any : $types.string);

      if (angular.isString(config.type))
        return $types[config.type];
      if (config.type instanceof Type)
        return config.type;
      return new Type(config.type);
    }

    // array config: param name (param[]) overrides default settings.  explicit config overrides param name.
    function getArrayMode() {
      var arrayDefaults = { array: (location === "search" ? "auto" : false) };
      var arrayParamNomenclature = id.match(/\[\]$/) ? { array: true } : {};
      return extend(arrayDefaults, arrayParamNomenclature, config).array;
    }

    /**
     * returns false, true, or the squash value to indicate the "default parameter url squash policy".
     */
    function getSquashPolicy(config, isOptional) {
      var squash = config.squash;
      if (!isOptional || squash === false) return false;
      if (!isDefined(squash) || squash == null) return defaultSquashPolicy;
      if (squash === true || isString(squash)) return squash;
      throw new Error("Invalid squash policy: '" + squash + "'. Valid policies: false, true, or arbitrary string");
    }

    function getReplace(config, arrayMode, isOptional, squash) {
      var replace, configuredKeys, defaultPolicy = [
        { from: "",   to: (isOptional || arrayMode ? undefined : "") },
        { from: null, to: (isOptional || arrayMode ? undefined : "") }
      ];
      replace = isArray(config.replace) ? config.replace : [];
      if (isString(squash))
        replace.push({ from: squash, to: undefined });
      configuredKeys = map(replace, function(item) { return item.from; } );
      return filter(defaultPolicy, function(item) { return indexOf(configuredKeys, item.from) === -1; }).concat(replace);
    }

    /**
     * [Internal] Get the default value of a parameter, which may be an injectable function.
     */
    function $$getDefaultValue() {
      if (!injector) throw new Error("Injectable functions cannot be called at configuration time");
      var defaultValue = injector.invoke(config.$$fn);
      if (defaultValue !== null && defaultValue !== undefined && !self.type.is(defaultValue))
        throw new Error("Default value (" + defaultValue + ") for parameter '" + self.id + "' is not an instance of Type (" + self.type.name + ")");
      return defaultValue;
    }

    /**
     * [Internal] Gets the decoded representation of a value if the value is defined, otherwise, returns the
     * default value, which may be the result of an injectable function.
     */
    function $value(value) {
      function hasReplaceVal(val) { return function(obj) { return obj.from === val; }; }
      function $replace(value) {
        var replacement = map(filter(self.replace, hasReplaceVal(value)), function(obj) { return obj.to; });
        return replacement.length ? replacement[0] : value;
      }
      value = $replace(value);
      return !isDefined(value) ? $$getDefaultValue() : self.type.$normalize(value);
    }

    function toString() { return "{Param:" + id + " " + type + " squash: '" + squash + "' optional: " + isOptional + "}"; }

    extend(this, {
      id: id,
      type: type,
      location: location,
      array: arrayMode,
      squash: squash,
      replace: replace,
      isOptional: isOptional,
      value: $value,
      dynamic: undefined,
      config: config,
      toString: toString
    });
  };

  function ParamSet(params) {
    extend(this, params || {});
  }

  ParamSet.prototype = {
    $$new: function() {
      return inherit(this, extend(new ParamSet(), { $$parent: this}));
    },
    $$keys: function () {
      var keys = [], chain = [], parent = this,
        ignore = objectKeys(ParamSet.prototype);
      while (parent) { chain.push(parent); parent = parent.$$parent; }
      chain.reverse();
      forEach(chain, function(paramset) {
        forEach(objectKeys(paramset), function(key) {
            if (indexOf(keys, key) === -1 && indexOf(ignore, key) === -1) keys.push(key);
        });
      });
      return keys;
    },
    $$values: function(paramValues) {
      var values = {}, self = this;
      forEach(self.$$keys(), function(key) {
        values[key] = self[key].value(paramValues && paramValues[key]);
      });
      return values;
    },
    $$equals: function(paramValues1, paramValues2) {
      var equal = true, self = this;
      forEach(self.$$keys(), function(key) {
        var left = paramValues1 && paramValues1[key], right = paramValues2 && paramValues2[key];
        if (!self[key].type.equals(left, right)) equal = false;
      });
      return equal;
    },
    $$validates: function $$validate(paramValues) {
      var keys = this.$$keys(), i, param, rawVal, normalized, encoded;
      for (i = 0; i < keys.length; i++) {
        param = this[keys[i]];
        rawVal = paramValues[keys[i]];
        if ((rawVal === undefined || rawVal === null) && param.isOptional)
          break; // There was no parameter value, but the param is optional
        normalized = param.type.$normalize(rawVal);
        if (!param.type.is(normalized))
          return false; // The value was not of the correct Type, and could not be decoded to the correct Type
        encoded = param.type.encode(normalized);
        if (angular.isString(encoded) && !param.type.pattern.exec(encoded))
          return false; // The value was of the correct type, but when encoded, did not match the Type's regexp
      }
      return true;
    },
    $$parent: undefined
  };

  this.ParamSet = ParamSet;
}

// Register as a provider so it's available to other providers
angular.module('ui.router.util').provider('$urlMatcherFactory', $UrlMatcherFactory);
angular.module('ui.router.util').run(['$urlMatcherFactory', function($urlMatcherFactory) { }]);

/**
 * @ngdoc object
 * @name ui.router.router.$urlRouterProvider
 *
 * @requires ui.router.util.$urlMatcherFactoryProvider
 * @requires $locationProvider
 *
 * @description
 * `$urlRouterProvider` has the responsibility of watching `$location`. 
 * When `$location` changes it runs through a list of rules one by one until a 
 * match is found. `$urlRouterProvider` is used behind the scenes anytime you specify 
 * a url in a state configuration. All urls are compiled into a UrlMatcher object.
 *
 * There are several methods on `$urlRouterProvider` that make it useful to use directly
 * in your module config.
 */
$UrlRouterProvider.$inject = ['$locationProvider', '$urlMatcherFactoryProvider'];
function $UrlRouterProvider(   $locationProvider,   $urlMatcherFactory) {
  var rules = [], otherwise = null, interceptDeferred = false, listener;

  // Returns a string that is a prefix of all strings matching the RegExp
  function regExpPrefix(re) {
    var prefix = /^\^((?:\\[^a-zA-Z0-9]|[^\\\[\]\^$*+?.()|{}]+)*)/.exec(re.source);
    return (prefix != null) ? prefix[1].replace(/\\(.)/g, "$1") : '';
  }

  // Interpolates matched values into a String.replace()-style pattern
  function interpolate(pattern, match) {
    return pattern.replace(/\$(\$|\d{1,2})/, function (m, what) {
      return match[what === '$' ? 0 : Number(what)];
    });
  }

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#rule
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Defines rules that are used by `$urlRouterProvider` to find matches for
   * specific URLs.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   // Here's an example of how you might allow case insensitive urls
   *   $urlRouterProvider.rule(function ($injector, $location) {
   *     var path = $location.path(),
   *         normalized = path.toLowerCase();
   *
   *     if (path !== normalized) {
   *       return normalized;
   *     }
   *   });
   * });
   * </pre>
   *
   * @param {function} rule Handler function that takes `$injector` and `$location`
   * services as arguments. You can use them to return a valid path as a string.
   *
   * @return {object} `$urlRouterProvider` - `$urlRouterProvider` instance
   */
  this.rule = function (rule) {
    if (!isFunction(rule)) throw new Error("'rule' must be a function");
    rules.push(rule);
    return this;
  };

  /**
   * @ngdoc object
   * @name ui.router.router.$urlRouterProvider#otherwise
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Defines a path that is used when an invalid route is requested.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   // if the path doesn't match any of the urls you configured
   *   // otherwise will take care of routing the user to the
   *   // specified url
   *   $urlRouterProvider.otherwise('/index');
   *
   *   // Example of using function rule as param
   *   $urlRouterProvider.otherwise(function ($injector, $location) {
   *     return '/a/valid/url';
   *   });
   * });
   * </pre>
   *
   * @param {string|function} rule The url path you want to redirect to or a function 
   * rule that returns the url path. The function version is passed two params: 
   * `$injector` and `$location` services, and must return a url string.
   *
   * @return {object} `$urlRouterProvider` - `$urlRouterProvider` instance
   */
  this.otherwise = function (rule) {
    if (isString(rule)) {
      var redirect = rule;
      rule = function () { return redirect; };
    }
    else if (!isFunction(rule)) throw new Error("'rule' must be a function");
    otherwise = rule;
    return this;
  };


  function handleIfMatch($injector, handler, match) {
    if (!match) return false;
    var result = $injector.invoke(handler, handler, { $match: match });
    return isDefined(result) ? result : true;
  }

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#when
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Registers a handler for a given url matching. 
   * 
   * If the handler is a string, it is
   * treated as a redirect, and is interpolated according to the syntax of match
   * (i.e. like `String.replace()` for `RegExp`, or like a `UrlMatcher` pattern otherwise).
   *
   * If the handler is a function, it is injectable. It gets invoked if `$location`
   * matches. You have the option of inject the match object as `$match`.
   *
   * The handler can return
   *
   * - **falsy** to indicate that the rule didn't match after all, then `$urlRouter`
   *   will continue trying to find another one that matches.
   * - **string** which is treated as a redirect and passed to `$location.url()`
   * - **void** or any **truthy** value tells `$urlRouter` that the url was handled.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *   $urlRouterProvider.when($state.url, function ($match, $stateParams) {
   *     if ($state.$current.navigable !== state ||
   *         !equalForKeys($match, $stateParams) {
   *      $state.transitionTo(state, $match, false);
   *     }
   *   });
   * });
   * </pre>
   *
   * @param {string|object} what The incoming path that you want to redirect.
   * @param {string|function} handler The path you want to redirect your user to.
   */
  this.when = function (what, handler) {
    var redirect, handlerIsString = isString(handler);
    if (isString(what)) what = $urlMatcherFactory.compile(what);

    if (!handlerIsString && !isFunction(handler) && !isArray(handler))
      throw new Error("invalid 'handler' in when()");

    var strategies = {
      matcher: function (what, handler) {
        if (handlerIsString) {
          redirect = $urlMatcherFactory.compile(handler);
          handler = ['$match', function ($match) { return redirect.format($match); }];
        }
        return extend(function ($injector, $location) {
          return handleIfMatch($injector, handler, what.exec($location.path(), $location.search()));
        }, {
          prefix: isString(what.prefix) ? what.prefix : ''
        });
      },
      regex: function (what, handler) {
        if (what.global || what.sticky) throw new Error("when() RegExp must not be global or sticky");

        if (handlerIsString) {
          redirect = handler;
          handler = ['$match', function ($match) { return interpolate(redirect, $match); }];
        }
        return extend(function ($injector, $location) {
          return handleIfMatch($injector, handler, what.exec($location.path()));
        }, {
          prefix: regExpPrefix(what)
        });
      }
    };

    var check = { matcher: $urlMatcherFactory.isMatcher(what), regex: what instanceof RegExp };

    for (var n in check) {
      if (check[n]) return this.rule(strategies[n](what, handler));
    }

    throw new Error("invalid 'what' in when()");
  };

  /**
   * @ngdoc function
   * @name ui.router.router.$urlRouterProvider#deferIntercept
   * @methodOf ui.router.router.$urlRouterProvider
   *
   * @description
   * Disables (or enables) deferring location change interception.
   *
   * If you wish to customize the behavior of syncing the URL (for example, if you wish to
   * defer a transition but maintain the current URL), call this method at configuration time.
   * Then, at run time, call `$urlRouter.listen()` after you have configured your own
   * `$locationChangeSuccess` event handler.
   *
   * @example
   * <pre>
   * var app = angular.module('app', ['ui.router.router']);
   *
   * app.config(function ($urlRouterProvider) {
   *
   *   // Prevent $urlRouter from automatically intercepting URL changes;
   *   // this allows you to configure custom behavior in between
   *   // location changes and route synchronization:
   *   $urlRouterProvider.deferIntercept();
   *
   * }).run(function ($rootScope, $urlRouter, UserService) {
   *
   *   $rootScope.$on('$locationChangeSuccess', function(e) {
   *     // UserService is an example service for managing user state
   *     if (UserService.isLoggedIn()) return;
   *
   *     // Prevent $urlRouter's default handler from firing
   *     e.preventDefault();
   *
   *     UserService.handleLogin().then(function() {
   *       // Once the user has logged in, sync the current URL
   *       // to the router:
   *       $urlRouter.sync();
   *     });
   *   });
   *
   *   // Configures $urlRouter's listener *after* your custom listener
   *   $urlRouter.listen();
   * });
   * </pre>
   *
   * @param {boolean} defer Indicates whether to defer location change interception. Passing
            no parameter is equivalent to `true`.
   */
  this.deferIntercept = function (defer) {
    if (defer === undefined) defer = true;
    interceptDeferred = defer;
  };

  /**
   * @ngdoc object
   * @name ui.router.router.$urlRouter
   *
   * @requires $location
   * @requires $rootScope
   * @requires $injector
   * @requires $browser
   *
   * @description
   *
   */
  this.$get = $get;
  $get.$inject = ['$location', '$rootScope', '$injector', '$browser', '$sniffer'];
  function $get(   $location,   $rootScope,   $injector,   $browser,   $sniffer) {

    var baseHref = $browser.baseHref(), location = $location.url(), lastPushedUrl;

    function appendBasePath(url, isHtml5, absolute) {
      if (baseHref === '/') return url;
      if (isHtml5) return baseHref.slice(0, -1) + url;
      if (absolute) return baseHref.slice(1) + url;
      return url;
    }

    // TODO: Optimize groups of rules with non-empty prefix into some sort of decision tree
    function update(evt) {
      if (evt && evt.defaultPrevented) return;
      var ignoreUpdate = lastPushedUrl && $location.url() === lastPushedUrl;
      lastPushedUrl = undefined;
      // TODO: Re-implement this in 1.0 for https://github.com/angular-ui/ui-router/issues/1573
      //if (ignoreUpdate) return true;

      function check(rule) {
        var handled = rule($injector, $location);

        if (!handled) return false;
        if (isString(handled)) $location.replace().url(handled);
        return true;
      }
      var n = rules.length, i;

      for (i = 0; i < n; i++) {
        if (check(rules[i])) return;
      }
      // always check otherwise last to allow dynamic updates to the set of rules
      if (otherwise) check(otherwise);
    }

    function listen() {
      listener = listener || $rootScope.$on('$locationChangeSuccess', update);
      return listener;
    }

    if (!interceptDeferred) listen();

    return {
      /**
       * @ngdoc function
       * @name ui.router.router.$urlRouter#sync
       * @methodOf ui.router.router.$urlRouter
       *
       * @description
       * Triggers an update; the same update that happens when the address bar url changes, aka `$locationChangeSuccess`.
       * This method is useful when you need to use `preventDefault()` on the `$locationChangeSuccess` event,
       * perform some custom logic (route protection, auth, config, redirection, etc) and then finally proceed
       * with the transition by calling `$urlRouter.sync()`.
       *
       * @example
       * <pre>
       * angular.module('app', ['ui.router'])
       *   .run(function($rootScope, $urlRouter) {
       *     $rootScope.$on('$locationChangeSuccess', function(evt) {
       *       // Halt state change from even starting
       *       evt.preventDefault();
       *       // Perform custom logic
       *       var meetsRequirement = ...
       *       // Continue with the update and state transition if logic allows
       *       if (meetsRequirement) $urlRouter.sync();
       *     });
       * });
       * </pre>
       */
      sync: function() {
        update();
      },

      listen: function() {
        return listen();
      },

      update: function(read) {
        if (read) {
          location = $location.url();
          return;
        }
        if ($location.url() === location) return;

        $location.url(location);
        $location.replace();
      },

      push: function(urlMatcher, params, options) {
         var url = urlMatcher.format(params || {});

        // Handle the special hash param, if needed
        if (url !== null && params && params['#']) {
            url += '#' + params['#'];
        }

        $location.url(url);
        lastPushedUrl = options && options.$$avoidResync ? $location.url() : undefined;
        if (options && options.replace) $location.replace();
      },

      /**
       * @ngdoc function
       * @name ui.router.router.$urlRouter#href
       * @methodOf ui.router.router.$urlRouter
       *
       * @description
       * A URL generation method that returns the compiled URL for a given
       * {@link ui.router.util.type:UrlMatcher `UrlMatcher`}, populated with the provided parameters.
       *
       * @example
       * <pre>
       * $bob = $urlRouter.href(new UrlMatcher("/about/:person"), {
       *   person: "bob"
       * });
       * // $bob == "/about/bob";
       * </pre>
       *
       * @param {UrlMatcher} urlMatcher The `UrlMatcher` object which is used as the template of the URL to generate.
       * @param {object=} params An object of parameter values to fill the matcher's required parameters.
       * @param {object=} options Options object. The options are:
       *
       * - **`absolute`** - {boolean=false},  If true will generate an absolute url, e.g. "http://www.example.com/fullurl".
       *
       * @returns {string} Returns the fully compiled URL, or `null` if `params` fail validation against `urlMatcher`
       */
      href: function(urlMatcher, params, options) {
        if (!urlMatcher.validates(params)) return null;

        var isHtml5 = $locationProvider.html5Mode();
        if (angular.isObject(isHtml5)) {
          isHtml5 = isHtml5.enabled;
        }

        isHtml5 = isHtml5 && $sniffer.history;
        
        var url = urlMatcher.format(params);
        options = options || {};

        if (!isHtml5 && url !== null) {
          url = "#" + $locationProvider.hashPrefix() + url;
        }

        // Handle special hash param, if needed
        if (url !== null && params && params['#']) {
          url += '#' + params['#'];
        }

        url = appendBasePath(url, isHtml5, options.absolute);

        if (!options.absolute || !url) {
          return url;
        }

        var slash = (!isHtml5 && url ? '/' : ''), port = $location.port();
        port = (port === 80 || port === 443 ? '' : ':' + port);

        return [$location.protocol(), '://', $location.host(), port, slash, url].join('');
      }
    };
  }
}

angular.module('ui.router.router').provider('$urlRouter', $UrlRouterProvider);

/**
 * @ngdoc object
 * @name ui.router.state.$stateProvider
 *
 * @requires ui.router.router.$urlRouterProvider
 * @requires ui.router.util.$urlMatcherFactoryProvider
 *
 * @description
 * The new `$stateProvider` works similar to Angular's v1 router, but it focuses purely
 * on state.
 *
 * A state corresponds to a "place" in the application in terms of the overall UI and
 * navigation. A state describes (via the controller / template / view properties) what
 * the UI looks like and does at that place.
 *
 * States often have things in common, and the primary way of factoring out these
 * commonalities in this model is via the state hierarchy, i.e. parent/child states aka
 * nested states.
 *
 * The `$stateProvider` provides interfaces to declare these states for your app.
 */
$StateProvider.$inject = ['$urlRouterProvider', '$urlMatcherFactoryProvider'];
function $StateProvider(   $urlRouterProvider,   $urlMatcherFactory) {

  var root, states = {}, $state, queue = {}, abstractKey = 'abstract';

  // Builds state properties from definition passed to registerState()
  var stateBuilder = {

    // Derive parent state from a hierarchical name only if 'parent' is not explicitly defined.
    // state.children = [];
    // if (parent) parent.children.push(state);
    parent: function(state) {
      if (isDefined(state.parent) && state.parent) return findState(state.parent);
      // regex matches any valid composite state name
      // would match "contact.list" but not "contacts"
      var compositeName = /^(.+)\.[^.]+$/.exec(state.name);
      return compositeName ? findState(compositeName[1]) : root;
    },

    // inherit 'data' from parent and override by own values (if any)
    data: function(state) {
      if (state.parent && state.parent.data) {
        state.data = state.self.data = inherit(state.parent.data, state.data);
      }
      return state.data;
    },

    // Build a URLMatcher if necessary, either via a relative or absolute URL
    url: function(state) {
      var url = state.url, config = { params: state.params || {} };

      if (isString(url)) {
        if (url.charAt(0) == '^') return $urlMatcherFactory.compile(url.substring(1), config);
        return (state.parent.navigable || root).url.concat(url, config);
      }

      if (!url || $urlMatcherFactory.isMatcher(url)) return url;
      throw new Error("Invalid url '" + url + "' in state '" + state + "'");
    },

    // Keep track of the closest ancestor state that has a URL (i.e. is navigable)
    navigable: function(state) {
      return state.url ? state : (state.parent ? state.parent.navigable : null);
    },

    // Own parameters for this state. state.url.params is already built at this point. Create and add non-url params
    ownParams: function(state) {
      var params = state.url && state.url.params || new $$UMFP.ParamSet();
      forEach(state.params || {}, function(config, id) {
        if (!params[id]) params[id] = new $$UMFP.Param(id, null, config, "config");
      });
      return params;
    },

    // Derive parameters for this state and ensure they're a super-set of parent's parameters
    params: function(state) {
      var ownParams = pick(state.ownParams, state.ownParams.$$keys());
      return state.parent && state.parent.params ? extend(state.parent.params.$$new(), ownParams) : new $$UMFP.ParamSet();
    },

    // If there is no explicit multi-view configuration, make one up so we don't have
    // to handle both cases in the view directive later. Note that having an explicit
    // 'views' property will mean the default unnamed view properties are ignored. This
    // is also a good time to resolve view names to absolute names, so everything is a
    // straight lookup at link time.
    views: function(state) {
      var views = {};

      forEach(isDefined(state.views) ? state.views : { '': state }, function (view, name) {
        if (name.indexOf('@') < 0) name += '@' + state.parent.name;
        view.resolveAs = view.resolveAs || state.resolveAs || '$resolve';
        views[name] = view;
      });
      return views;
    },

    // Keep a full path from the root down to this state as this is needed for state activation.
    path: function(state) {
      return state.parent ? state.parent.path.concat(state) : []; // exclude root from path
    },

    // Speed up $state.contains() as it's used a lot
    includes: function(state) {
      var includes = state.parent ? extend({}, state.parent.includes) : {};
      includes[state.name] = true;
      return includes;
    },

    $delegates: {}
  };

  function isRelative(stateName) {
    return stateName.indexOf(".") === 0 || stateName.indexOf("^") === 0;
  }

  function findState(stateOrName, base) {
    if (!stateOrName) return undefined;

    var isStr = isString(stateOrName),
        name  = isStr ? stateOrName : stateOrName.name,
        path  = isRelative(name);

    if (path) {
      if (!base) throw new Error("No reference point given for path '"  + name + "'");
      base = findState(base);
      
      var rel = name.split("."), i = 0, pathLength = rel.length, current = base;

      for (; i < pathLength; i++) {
        if (rel[i] === "" && i === 0) {
          current = base;
          continue;
        }
        if (rel[i] === "^") {
          if (!current.parent) throw new Error("Path '" + name + "' not valid for state '" + base.name + "'");
          current = current.parent;
          continue;
        }
        break;
      }
      rel = rel.slice(i).join(".");
      name = current.name + (current.name && rel ? "." : "") + rel;
    }
    var state = states[name];

    if (state && (isStr || (!isStr && (state === stateOrName || state.self === stateOrName)))) {
      return state;
    }
    return undefined;
  }

  function queueState(parentName, state) {
    if (!queue[parentName]) {
      queue[parentName] = [];
    }
    queue[parentName].push(state);
  }

  function flushQueuedChildren(parentName) {
    var queued = queue[parentName] || [];
    while(queued.length) {
      registerState(queued.shift());
    }
  }

  function registerState(state) {
    // Wrap a new object around the state so we can store our private details easily.
    state = inherit(state, {
      self: state,
      resolve: state.resolve || {},
      toString: function() { return this.name; }
    });

    var name = state.name;
    if (!isString(name) || name.indexOf('@') >= 0) throw new Error("State must have a valid name");
    if (states.hasOwnProperty(name)) throw new Error("State '" + name + "' is already defined");

    // Get parent name
    var parentName = (name.indexOf('.') !== -1) ? name.substring(0, name.lastIndexOf('.'))
        : (isString(state.parent)) ? state.parent
        : (isObject(state.parent) && isString(state.parent.name)) ? state.parent.name
        : '';

    // If parent is not registered yet, add state to queue and register later
    if (parentName && !states[parentName]) {
      return queueState(parentName, state.self);
    }

    for (var key in stateBuilder) {
      if (isFunction(stateBuilder[key])) state[key] = stateBuilder[key](state, stateBuilder.$delegates[key]);
    }
    states[name] = state;

    // Register the state in the global state list and with $urlRouter if necessary.
    if (!state[abstractKey] && state.url) {
      $urlRouterProvider.when(state.url, ['$match', '$stateParams', function ($match, $stateParams) {
        if ($state.$current.navigable != state || !equalForKeys($match, $stateParams)) {
          $state.transitionTo(state, $match, { inherit: true, location: false });
        }
      }]);
    }

    // Register any queued children
    flushQueuedChildren(name);

    return state;
  }

  // Checks text to see if it looks like a glob.
  function isGlob (text) {
    return text.indexOf('*') > -1;
  }

  // Returns true if glob matches current $state name.
  function doesStateMatchGlob (glob) {
    var globSegments = glob.split('.'),
        segments = $state.$current.name.split('.');

    //match single stars
    for (var i = 0, l = globSegments.length; i < l; i++) {
      if (globSegments[i] === '*') {
        segments[i] = '*';
      }
    }

    //match greedy starts
    if (globSegments[0] === '**') {
       segments = segments.slice(indexOf(segments, globSegments[1]));
       segments.unshift('**');
    }
    //match greedy ends
    if (globSegments[globSegments.length - 1] === '**') {
       segments.splice(indexOf(segments, globSegments[globSegments.length - 2]) + 1, Number.MAX_VALUE);
       segments.push('**');
    }

    if (globSegments.length != segments.length) {
      return false;
    }

    return segments.join('') === globSegments.join('');
  }


  // Implicit root state that is always active
  root = registerState({
    name: '',
    url: '^',
    views: null,
    'abstract': true
  });
  root.navigable = null;


  /**
   * @ngdoc function
   * @name ui.router.state.$stateProvider#decorator
   * @methodOf ui.router.state.$stateProvider
   *
   * @description
   * Allows you to extend (carefully) or override (at your own peril) the 
   * `stateBuilder` object used internally by `$stateProvider`. This can be used 
   * to add custom functionality to ui-router, for example inferring templateUrl 
   * based on the state name.
   *
   * When passing only a name, it returns the current (original or decorated) builder
   * function that matches `name`.
   *
   * The builder functions that can be decorated are listed below. Though not all
   * necessarily have a good use case for decoration, that is up to you to decide.
   *
   * In addition, users can attach custom decorators, which will generate new 
   * properties within the state's internal definition. There is currently no clear 
   * use-case for this beyond accessing internal states (i.e. $state.$current), 
   * however, expect this to become increasingly relevant as we introduce additional 
   * meta-programming features.
   *
   * **Warning**: Decorators should not be interdependent because the order of 
   * execution of the builder functions in non-deterministic. Builder functions 
   * should only be dependent on the state definition object and super function.
   *
   *
   * Existing builder functions and current return values:
   *
   * - **parent** `{object}` - returns the parent state object.
   * - **data** `{object}` - returns state data, including any inherited data that is not
   *   overridden by own values (if any).
   * - **url** `{object}` - returns a {@link ui.router.util.type:UrlMatcher UrlMatcher}
   *   or `null`.
   * - **navigable** `{object}` - returns closest ancestor state that has a URL (aka is 
   *   navigable).
   * - **params** `{object}` - returns an array of state params that are ensured to 
   *   be a super-set of parent's params.
   * - **views** `{object}` - returns a views object where each key is an absolute view 
   *   name (i.e. "viewName@stateName") and each value is the config object 
   *   (template, controller) for the view. Even when you don't use the views object 
   *   explicitly on a state config, one is still created for you internally.
   *   So by decorating this builder function you have access to decorating template 
   *   and controller properties.
   * - **ownParams** `{object}` - returns an array of params that belong to the state, 
   *   not including any params defined by ancestor states.
   * - **path** `{string}` - returns the full path from the root down to this state. 
   *   Needed for state activation.
   * - **includes** `{object}` - returns an object that includes every state that 
   *   would pass a `$state.includes()` test.
   *
   * @example
   * <pre>
   * // Override the internal 'views' builder with a function that takes the state
   * // definition, and a reference to the internal function being overridden:
   * $stateProvider.decorator('views', function (state, parent) {
   *   var result = {},
   *       views = parent(state);
   *
   *   angular.forEach(views, function (config, name) {
   *     var autoName = (state.name + '.' + name).replace('.', '/');
   *     config.templateUrl = config.templateUrl || '/partials/' + autoName + '.html';
   *     result[name] = config;
   *   });
   *   return result;
   * });
   *
   * $stateProvider.state('home', {
   *   views: {
   *     'contact.list': { controller: 'ListController' },
   *     'contact.item': { controller: 'ItemController' }
   *   }
   * });
   *
   * // ...
   *
   * $state.go('home');
   * // Auto-populates list and item views with /partials/home/contact/list.html,
   * // and /partials/home/contact/item.html, respectively.
   * </pre>
   *
   * @param {string} name The name of the builder function to decorate. 
   * @param {object} func A function that is responsible for decorating the original 
   * builder function. The function receives two parameters:
   *
   *   - `{object}` - state - The state config object.
   *   - `{object}` - super - The original builder function.
   *
   * @return {object} $stateProvider - $stateProvider instance
   */
  this.decorator = decorator;
  function decorator(name, func) {
    /*jshint validthis: true */
    if (isString(name) && !isDefined(func)) {
      return stateBuilder[name];
    }
    if (!isFunction(func) || !isString(name)) {
      return this;
    }
    if (stateBuilder[name] && !stateBuilder.$delegates[name]) {
      stateBuilder.$delegates[name] = stateBuilder[name];
    }
    stateBuilder[name] = func;
    return this;
  }

  /**
   * @ngdoc function
   * @name ui.router.state.$stateProvider#state
   * @methodOf ui.router.state.$stateProvider
   *
   * @description
   * Registers a state configuration under a given state name. The stateConfig object
   * has the following acceptable properties.
   *
   * @param {string} name A unique state name, e.g. "home", "about", "contacts".
   * To create a parent/child state use a dot, e.g. "about.sales", "home.newest".
   * @param {object} stateConfig State configuration object.
   * @param {string|function=} stateConfig.template
   * <a id='template'></a>
   *   html template as a string or a function that returns
   *   an html template as a string which should be used by the uiView directives. This property 
   *   takes precedence over templateUrl.
   *   
   *   If `template` is a function, it will be called with the following parameters:
   *
   *   - {array.&lt;object&gt;} - state parameters extracted from the current $location.path() by
   *     applying the current state
   *
   * <pre>template:
   *   "<h1>inline template definition</h1>" +
   *   "<div ui-view></div>"</pre>
   * <pre>template: function(params) {
   *       return "<h1>generated template</h1>"; }</pre>
   * </div>
   *
   * @param {string|function=} stateConfig.templateUrl
   * <a id='templateUrl'></a>
   *
   *   path or function that returns a path to an html
   *   template that should be used by uiView.
   *   
   *   If `templateUrl` is a function, it will be called with the following parameters:
   *
   *   - {array.&lt;object&gt;} - state parameters extracted from the current $location.path() by 
   *     applying the current state
   *
   * <pre>templateUrl: "home.html"</pre>
   * <pre>templateUrl: function(params) {
   *     return myTemplates[params.pageId]; }</pre>
   *
   * @param {function=} stateConfig.templateProvider
   * <a id='templateProvider'></a>
   *    Provider function that returns HTML content string.
   * <pre> templateProvider:
   *       function(MyTemplateService, params) {
   *         return MyTemplateService.getTemplate(params.pageId);
   *       }</pre>
   *
   * @param {string|function=} stateConfig.controller
   * <a id='controller'></a>
   *
   *  Controller fn that should be associated with newly
   *   related scope or the name of a registered controller if passed as a string.
   *   Optionally, the ControllerAs may be declared here.
   * <pre>controller: "MyRegisteredController"</pre>
   * <pre>controller:
   *     "MyRegisteredController as fooCtrl"}</pre>
   * <pre>controller: function($scope, MyService) {
   *     $scope.data = MyService.getData(); }</pre>
   *
   * @param {function=} stateConfig.controllerProvider
   * <a id='controllerProvider'></a>
   *
   * Injectable provider function that returns the actual controller or string.
   * <pre>controllerProvider:
   *   function(MyResolveData) {
   *     if (MyResolveData.foo)
   *       return "FooCtrl"
   *     else if (MyResolveData.bar)
   *       return "BarCtrl";
   *     else return function($scope) {
   *       $scope.baz = "Qux";
   *     }
   *   }</pre>
   *
   * @param {string=} stateConfig.controllerAs
   * <a id='controllerAs'></a>
   * 
   * A controller alias name. If present the controller will be
   *   published to scope under the controllerAs name.
   * <pre>controllerAs: "myCtrl"</pre>
   *
   * @param {string|object=} stateConfig.parent
   * <a id='parent'></a>
   * Optionally specifies the parent state of this state.
   *
   * <pre>parent: 'parentState'</pre>
   * <pre>parent: parentState // JS variable</pre>
   *
   * @param {object=} stateConfig.resolve
   * <a id='resolve'></a>
   *
   * An optional map&lt;string, function&gt; of dependencies which
   *   should be injected into the controller. If any of these dependencies are promises, 
   *   the router will wait for them all to be resolved before the controller is instantiated.
   *   If all the promises are resolved successfully, the $stateChangeSuccess event is fired
   *   and the values of the resolved promises are injected into any controllers that reference them.
   *   If any  of the promises are rejected the $stateChangeError event is fired.
   *
   *   The map object is:
   *   
   *   - key - {string}: name of dependency to be injected into controller
   *   - factory - {string|function}: If string then it is alias for service. Otherwise if function, 
   *     it is injected and return value it treated as dependency. If result is a promise, it is 
   *     resolved before its value is injected into controller.
   *
   * <pre>resolve: {
   *     myResolve1:
   *       function($http, $stateParams) {
   *         return $http.get("/api/foos/"+stateParams.fooID);
   *       }
   *     }</pre>
   *
   * @param {string=} stateConfig.url
   * <a id='url'></a>
   *
   *   A url fragment with optional parameters. When a state is navigated or
   *   transitioned to, the `$stateParams` service will be populated with any 
   *   parameters that were passed.
   *
   *   (See {@link ui.router.util.type:UrlMatcher UrlMatcher} `UrlMatcher`} for
   *   more details on acceptable patterns )
   *
   * examples:
   * <pre>url: "/home"
   * url: "/users/:userid"
   * url: "/books/{bookid:[a-zA-Z_-]}"
   * url: "/books/{categoryid:int}"
   * url: "/books/{publishername:string}/{categoryid:int}"
   * url: "/messages?before&after"
   * url: "/messages?{before:date}&{after:date}"
   * url: "/messages/:mailboxid?{before:date}&{after:date}"
   * </pre>
   *
   * @param {object=} stateConfig.views
   * <a id='views'></a>
   * an optional map&lt;string, object&gt; which defined multiple views, or targets views
   * manually/explicitly.
   *
   * Examples:
   *
   * Targets three named `ui-view`s in the parent state's template
   * <pre>views: {
   *     header: {
   *       controller: "headerCtrl",
   *       templateUrl: "header.html"
   *     }, body: {
   *       controller: "bodyCtrl",
   *       templateUrl: "body.html"
   *     }, footer: {
   *       controller: "footCtrl",
   *       templateUrl: "footer.html"
   *     }
   *   }</pre>
   *
   * Targets named `ui-view="header"` from grandparent state 'top''s template, and named `ui-view="body" from parent state's template.
   * <pre>views: {
   *     'header@top': {
   *       controller: "msgHeaderCtrl",
   *       templateUrl: "msgHeader.html"
   *     }, 'body': {
   *       controller: "messagesCtrl",
   *       templateUrl: "messages.html"
   *     }
   *   }</pre>
   *
   * @param {boolean=} [stateConfig.abstract=false]
   * <a id='abstract'></a>
   * An abstract state will never be directly activated,
   *   but can provide inherited properties to its common children states.
   * <pre>abstract: true</pre>
   *
   * @param {function=} stateConfig.onEnter
   * <a id='onEnter'></a>
   *
   * Callback function for when a state is entered. Good way
   *   to trigger an action or dispatch an event, such as opening a dialog.
   * If minifying your scripts, make sure to explicitly annotate this function,
   * because it won't be automatically annotated by your build tools.
   *
   * <pre>onEnter: function(MyService, $stateParams) {
   *     MyService.foo($stateParams.myParam);
   * }</pre>
   *
   * @param {function=} stateConfig.onExit
   * <a id='onExit'></a>
   *
   * Callback function for when a state is exited. Good way to
   *   trigger an action or dispatch an event, such as opening a dialog.
   * If minifying your scripts, make sure to explicitly annotate this function,
   * because it won't be automatically annotated by your build tools.
   *
   * <pre>onExit: function(MyService, $stateParams) {
   *     MyService.cleanup($stateParams.myParam);
   * }</pre>
   *
   * @param {boolean=} [stateConfig.reloadOnSearch=true]
   * <a id='reloadOnSearch'></a>
   *
   * If `false`, will not retrigger the same state
   *   just because a search/query parameter has changed (via $location.search() or $location.hash()). 
   *   Useful for when you'd like to modify $location.search() without triggering a reload.
   * <pre>reloadOnSearch: false</pre>
   *
   * @param {object=} stateConfig.data
   * <a id='data'></a>
   *
   * Arbitrary data object, useful for custom configuration.  The parent state's `data` is
   *   prototypally inherited.  In other words, adding a data property to a state adds it to
   *   the entire subtree via prototypal inheritance.
   *
   * <pre>data: {
   *     requiredRole: 'foo'
   * } </pre>
   *
   * @param {object=} stateConfig.params
   * <a id='params'></a>
   *
   * A map which optionally configures parameters declared in the `url`, or
   *   defines additional non-url parameters.  For each parameter being
   *   configured, add a configuration object keyed to the name of the parameter.
   *
   *   Each parameter configuration object may contain the following properties:
   *
   *   - ** value ** - {object|function=}: specifies the default value for this
   *     parameter.  This implicitly sets this parameter as optional.
   *
   *     When UI-Router routes to a state and no value is
   *     specified for this parameter in the URL or transition, the
   *     default value will be used instead.  If `value` is a function,
   *     it will be injected and invoked, and the return value used.
   *
   *     *Note*: `undefined` is treated as "no default value" while `null`
   *     is treated as "the default value is `null`".
   *
   *     *Shorthand*: If you only need to configure the default value of the
   *     parameter, you may use a shorthand syntax.   In the **`params`**
   *     map, instead mapping the param name to a full parameter configuration
   *     object, simply set map it to the default parameter value, e.g.:
   *
   * <pre>// define a parameter's default value
   * params: {
   *     param1: { value: "defaultValue" }
   * }
   * // shorthand default values
   * params: {
   *     param1: "defaultValue",
   *     param2: "param2Default"
   * }</pre>
   *
   *   - ** array ** - {boolean=}: *(default: false)* If true, the param value will be
   *     treated as an array of values.  If you specified a Type, the value will be
   *     treated as an array of the specified Type.  Note: query parameter values
   *     default to a special `"auto"` mode.
   *
   *     For query parameters in `"auto"` mode, if multiple  values for a single parameter
   *     are present in the URL (e.g.: `/foo?bar=1&bar=2&bar=3`) then the values
   *     are mapped to an array (e.g.: `{ foo: [ '1', '2', '3' ] }`).  However, if
   *     only one value is present (e.g.: `/foo?bar=1`) then the value is treated as single
   *     value (e.g.: `{ foo: '1' }`).
   *
   * <pre>params: {
   *     param1: { array: true }
   * }</pre>
   *
   *   - ** squash ** - {bool|string=}: `squash` configures how a default parameter value is represented in the URL when
   *     the current parameter value is the same as the default value. If `squash` is not set, it uses the
   *     configured default squash policy.
   *     (See {@link ui.router.util.$urlMatcherFactory#methods_defaultSquashPolicy `defaultSquashPolicy()`})
   *
   *   There are three squash settings:
   *
   *     - false: The parameter's default value is not squashed.  It is encoded and included in the URL
   *     - true: The parameter's default value is omitted from the URL.  If the parameter is preceeded and followed
   *       by slashes in the state's `url` declaration, then one of those slashes are omitted.
   *       This can allow for cleaner looking URLs.
   *     - `"<arbitrary string>"`: The parameter's default value is replaced with an arbitrary placeholder of  your choice.
   *
   * <pre>params: {
   *     param1: {
   *       value: "defaultId",
   *       squash: true
   * } }
   * // squash "defaultValue" to "~"
   * params: {
   *     param1: {
   *       value: "defaultValue",
   *       squash: "~"
   * } }
   * </pre>
   *
   *
   * @example
   * <pre>
   * // Some state name examples
   *
   * // stateName can be a single top-level name (must be unique).
   * $stateProvider.state("home", {});
   *
   * // Or it can be a nested state name. This state is a child of the
   * // above "home" state.
   * $stateProvider.state("home.newest", {});
   *
   * // Nest states as deeply as needed.
   * $stateProvider.state("home.newest.abc.xyz.inception", {});
   *
   * // state() returns $stateProvider, so you can chain state declarations.
   * $stateProvider
   *   .state("home", {})
   *   .state("about", {})
   *   .state("contacts", {});
   * </pre>
   *
   */
  this.state = state;
  function state(name, definition) {
    /*jshint validthis: true */
    if (isObject(name)) definition = name;
    else definition.name = name;
    registerState(definition);
    return this;
  }

  /**
   * @ngdoc object
   * @name ui.router.state.$state
   *
   * @requires $rootScope
   * @requires $q
   * @requires ui.router.state.$view
   * @requires $injector
   * @requires ui.router.util.$resolve
   * @requires ui.router.state.$stateParams
   * @requires ui.router.router.$urlRouter
   *
   * @property {object} params A param object, e.g. {sectionId: section.id)}, that 
   * you'd like to test against the current active state.
   * @property {object} current A reference to the state's config object. However 
   * you passed it in. Useful for accessing custom data.
   * @property {object} transition Currently pending transition. A promise that'll 
   * resolve or reject.
   *
   * @description
   * `$state` service is responsible for representing states as well as transitioning
   * between them. It also provides interfaces to ask for current state or even states
   * you're coming from.
   */
  this.$get = $get;
  $get.$inject = ['$rootScope', '$q', '$view', '$injector', '$resolve', '$stateParams', '$urlRouter', '$location', '$urlMatcherFactory'];
  function $get(   $rootScope,   $q,   $view,   $injector,   $resolve,   $stateParams,   $urlRouter,   $location,   $urlMatcherFactory) {

    var TransitionSupersededError = new Error('transition superseded');

    var TransitionSuperseded = silenceUncaughtInPromise($q.reject(TransitionSupersededError));
    var TransitionPrevented = silenceUncaughtInPromise($q.reject(new Error('transition prevented')));
    var TransitionAborted = silenceUncaughtInPromise($q.reject(new Error('transition aborted')));
    var TransitionFailed = silenceUncaughtInPromise($q.reject(new Error('transition failed')));

    // Handles the case where a state which is the target of a transition is not found, and the user
    // can optionally retry or defer the transition
    function handleRedirect(redirect, state, params, options) {
      /**
       * @ngdoc event
       * @name ui.router.state.$state#$stateNotFound
       * @eventOf ui.router.state.$state
       * @eventType broadcast on root scope
       * @description
       * Fired when a requested state **cannot be found** using the provided state name during transition.
       * The event is broadcast allowing any handlers a single chance to deal with the error (usually by
       * lazy-loading the unfound state). A special `unfoundState` object is passed to the listener handler,
       * you can see its three properties in the example. You can use `event.preventDefault()` to abort the
       * transition and the promise returned from `go` will be rejected with a `'transition aborted'` value.
       *
       * @param {Object} event Event object.
       * @param {Object} unfoundState Unfound State information. Contains: `to, toParams, options` properties.
       * @param {State} fromState Current state object.
       * @param {Object} fromParams Current state params.
       *
       * @example
       *
       * <pre>
       * // somewhere, assume lazy.state has not been defined
       * $state.go("lazy.state", {a:1, b:2}, {inherit:false});
       *
       * // somewhere else
       * $scope.$on('$stateNotFound',
       * function(event, unfoundState, fromState, fromParams){
       *     console.log(unfoundState.to); // "lazy.state"
       *     console.log(unfoundState.toParams); // {a:1, b:2}
       *     console.log(unfoundState.options); // {inherit:false} + default options
       * })
       * </pre>
       */
      var evt = $rootScope.$broadcast('$stateNotFound', redirect, state, params);

      if (evt.defaultPrevented) {
        $urlRouter.update();
        return TransitionAborted;
      }

      if (!evt.retry) {
        return null;
      }

      // Allow the handler to return a promise to defer state lookup retry
      if (options.$retry) {
        $urlRouter.update();
        return TransitionFailed;
      }
      var retryTransition = $state.transition = $q.when(evt.retry);

      retryTransition.then(function() {
        if (retryTransition !== $state.transition) {
          $rootScope.$broadcast('$stateChangeCancel', redirect.to, redirect.toParams, state, params);
          return TransitionSuperseded;
        }
        redirect.options.$retry = true;
        return $state.transitionTo(redirect.to, redirect.toParams, redirect.options);
      }, function() {
        return TransitionAborted;
      });
      $urlRouter.update();

      return retryTransition;
    }

    root.locals = { resolve: null, globals: { $stateParams: {} } };

    $state = {
      params: {},
      current: root.self,
      $current: root,
      transition: null
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#reload
     * @methodOf ui.router.state.$state
     *
     * @description
     * A method that force reloads the current state. All resolves are re-resolved,
     * controllers reinstantiated, and events re-fired.
     *
     * @example
     * <pre>
     * var app angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.reload = function(){
     *     $state.reload();
     *   }
     * });
     * </pre>
     *
     * `reload()` is just an alias for:
     * <pre>
     * $state.transitionTo($state.current, $stateParams, { 
     *   reload: true, inherit: false, notify: true
     * });
     * </pre>
     *
     * @param {string=|object=} state - A state name or a state object, which is the root of the resolves to be re-resolved.
     * @example
     * <pre>
     * //assuming app application consists of 3 states: 'contacts', 'contacts.detail', 'contacts.detail.item' 
     * //and current state is 'contacts.detail.item'
     * var app angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.reload = function(){
     *     //will reload 'contact.detail' and 'contact.detail.item' states
     *     $state.reload('contact.detail');
     *   }
     * });
     * </pre>
     *
     * `reload()` is just an alias for:
     * <pre>
     * $state.transitionTo($state.current, $stateParams, { 
     *   reload: true, inherit: false, notify: true
     * });
     * </pre>

     * @returns {promise} A promise representing the state of the new transition. See
     * {@link ui.router.state.$state#methods_go $state.go}.
     */
    $state.reload = function reload(state) {
      return $state.transitionTo($state.current, $stateParams, { reload: state || true, inherit: false, notify: true});
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#go
     * @methodOf ui.router.state.$state
     *
     * @description
     * Convenience method for transitioning to a new state. `$state.go` calls 
     * `$state.transitionTo` internally but automatically sets options to 
     * `{ location: true, inherit: true, relative: $state.$current, notify: true }`. 
     * This allows you to easily use an absolute or relative to path and specify 
     * only the parameters you'd like to update (while letting unspecified parameters 
     * inherit from the currently active ancestor states).
     *
     * @example
     * <pre>
     * var app = angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.changeState = function () {
     *     $state.go('contact.detail');
     *   };
     * });
     * </pre>
     * <img src='../ngdoc_assets/StateGoExamples.png'/>
     *
     * @param {string} to Absolute state name or relative state path. Some examples:
     *
     * - `$state.go('contact.detail')` - will go to the `contact.detail` state
     * - `$state.go('^')` - will go to a parent state
     * - `$state.go('^.sibling')` - will go to a sibling state
     * - `$state.go('.child.grandchild')` - will go to grandchild state
     *
     * @param {object=} params A map of the parameters that will be sent to the state, 
     * will populate $stateParams. Any parameters that are not specified will be inherited from currently 
     * defined parameters. Only parameters specified in the state definition can be overridden, new 
     * parameters will be ignored. This allows, for example, going to a sibling state that shares parameters
     * specified in a parent state. Parameter inheritance only works between common ancestor states, I.e.
     * transitioning to a sibling will get you the parameters for all parents, transitioning to a child
     * will get you all current parameters, etc.
     * @param {object=} options Options object. The options are:
     *
     * - **`location`** - {boolean=true|string=} - If `true` will update the url in the location bar, if `false`
     *    will not. If string, must be `"replace"`, which will update url and also replace last history record.
     * - **`inherit`** - {boolean=true}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=$state.$current}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`notify`** - {boolean=true}, If `true` will broadcast $stateChangeStart and $stateChangeSuccess events.
     * - **`reload`** (v0.2.5) - {boolean=false|string|object}, If `true` will force transition even if no state or params
     *    have changed.  It will reload the resolves and views of the current state and parent states.
     *    If `reload` is a string (or state object), the state object is fetched (by name, or object reference); and \
     *    the transition reloads the resolves and views for that matched state, and all its children states.
     *
     * @returns {promise} A promise representing the state of the new transition.
     *
     * Possible success values:
     *
     * - $state.current
     *
     * <br/>Possible rejection values:
     *
     * - 'transition superseded' - when a newer transition has been started after this one
     * - 'transition prevented' - when `event.preventDefault()` has been called in a `$stateChangeStart` listener
     * - 'transition aborted' - when `event.preventDefault()` has been called in a `$stateNotFound` listener or
     *   when a `$stateNotFound` `event.retry` promise errors.
     * - 'transition failed' - when a state has been unsuccessfully found after 2 tries.
     * - *resolve error* - when an error has occurred with a `resolve`
     *
     */
    $state.go = function go(to, params, options) {
      return $state.transitionTo(to, params, extend({ inherit: true, relative: $state.$current }, options));
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#transitionTo
     * @methodOf ui.router.state.$state
     *
     * @description
     * Low-level method for transitioning to a new state. {@link ui.router.state.$state#methods_go $state.go}
     * uses `transitionTo` internally. `$state.go` is recommended in most situations.
     *
     * @example
     * <pre>
     * var app = angular.module('app', ['ui.router']);
     *
     * app.controller('ctrl', function ($scope, $state) {
     *   $scope.changeState = function () {
     *     $state.transitionTo('contact.detail');
     *   };
     * });
     * </pre>
     *
     * @param {string} to State name.
     * @param {object=} toParams A map of the parameters that will be sent to the state,
     * will populate $stateParams.
     * @param {object=} options Options object. The options are:
     *
     * - **`location`** - {boolean=true|string=} - If `true` will update the url in the location bar, if `false`
     *    will not. If string, must be `"replace"`, which will update url and also replace last history record.
     * - **`inherit`** - {boolean=false}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`notify`** - {boolean=true}, If `true` will broadcast $stateChangeStart and $stateChangeSuccess events.
     * - **`reload`** (v0.2.5) - {boolean=false|string=|object=}, If `true` will force transition even if the state or params 
     *    have not changed, aka a reload of the same state. It differs from reloadOnSearch because you'd
     *    use this when you want to force a reload when *everything* is the same, including search params.
     *    if String, then will reload the state with the name given in reload, and any children.
     *    if Object, then a stateObj is expected, will reload the state found in stateObj, and any children.
     *
     * @returns {promise} A promise representing the state of the new transition. See
     * {@link ui.router.state.$state#methods_go $state.go}.
     */
    $state.transitionTo = function transitionTo(to, toParams, options) {
      toParams = toParams || {};
      options = extend({
        location: true, inherit: false, relative: null, notify: true, reload: false, $retry: false
      }, options || {});

      var from = $state.$current, fromParams = $state.params, fromPath = from.path;
      var evt, toState = findState(to, options.relative);

      // Store the hash param for later (since it will be stripped out by various methods)
      var hash = toParams['#'];

      if (!isDefined(toState)) {
        var redirect = { to: to, toParams: toParams, options: options };
        var redirectResult = handleRedirect(redirect, from.self, fromParams, options);

        if (redirectResult) {
          return redirectResult;
        }

        // Always retry once if the $stateNotFound was not prevented
        // (handles either redirect changed or state lazy-definition)
        to = redirect.to;
        toParams = redirect.toParams;
        options = redirect.options;
        toState = findState(to, options.relative);

        if (!isDefined(toState)) {
          if (!options.relative) throw new Error("No such state '" + to + "'");
          throw new Error("Could not resolve '" + to + "' from state '" + options.relative + "'");
        }
      }
      if (toState[abstractKey]) throw new Error("Cannot transition to abstract state '" + to + "'");
      if (options.inherit) toParams = inheritParams($stateParams, toParams || {}, $state.$current, toState);
      if (!toState.params.$$validates(toParams)) return TransitionFailed;

      toParams = toState.params.$$values(toParams);
      to = toState;

      var toPath = to.path;

      // Starting from the root of the path, keep all levels that haven't changed
      var keep = 0, state = toPath[keep], locals = root.locals, toLocals = [];

      if (!options.reload) {
        while (state && state === fromPath[keep] && state.ownParams.$$equals(toParams, fromParams)) {
          locals = toLocals[keep] = state.locals;
          keep++;
          state = toPath[keep];
        }
      } else if (isString(options.reload) || isObject(options.reload)) {
        if (isObject(options.reload) && !options.reload.name) {
          throw new Error('Invalid reload state object');
        }
        
        var reloadState = options.reload === true ? fromPath[0] : findState(options.reload);
        if (options.reload && !reloadState) {
          throw new Error("No such reload state '" + (isString(options.reload) ? options.reload : options.reload.name) + "'");
        }

        while (state && state === fromPath[keep] && state !== reloadState) {
          locals = toLocals[keep] = state.locals;
          keep++;
          state = toPath[keep];
        }
      }

      // If we're going to the same state and all locals are kept, we've got nothing to do.
      // But clear 'transition', as we still want to cancel any other pending transitions.
      // TODO: We may not want to bump 'transition' if we're called from a location change
      // that we've initiated ourselves, because we might accidentally abort a legitimate
      // transition initiated from code?
      if (shouldSkipReload(to, toParams, from, fromParams, locals, options)) {
        if (hash) toParams['#'] = hash;
        $state.params = toParams;
        copy($state.params, $stateParams);
        copy(filterByKeys(to.params.$$keys(), $stateParams), to.locals.globals.$stateParams);
        if (options.location && to.navigable && to.navigable.url) {
          $urlRouter.push(to.navigable.url, toParams, {
            $$avoidResync: true, replace: options.location === 'replace'
          });
          $urlRouter.update(true);
        }
        $state.transition = null;
        return $q.when($state.current);
      }

      // Filter parameters before we pass them to event handlers etc.
      toParams = filterByKeys(to.params.$$keys(), toParams || {});
      
      // Re-add the saved hash before we start returning things or broadcasting $stateChangeStart
      if (hash) toParams['#'] = hash;
      
      // Broadcast start event and cancel the transition if requested
      if (options.notify) {
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeStart
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired when the state transition **begins**. You can use `event.preventDefault()`
         * to prevent the transition from happening and then the transition promise will be
         * rejected with a `'transition prevented'` value.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         *
         * @example
         *
         * <pre>
         * $rootScope.$on('$stateChangeStart',
         * function(event, toState, toParams, fromState, fromParams){
         *     event.preventDefault();
         *     // transitionTo() promise will be rejected with
         *     // a 'transition prevented' error
         * })
         * </pre>
         */
        if ($rootScope.$broadcast('$stateChangeStart', to.self, toParams, from.self, fromParams, options).defaultPrevented) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          //Don't update and resync url if there's been a new transition started. see issue #2238, #600
          if ($state.transition == null) $urlRouter.update();
          return TransitionPrevented;
        }
      }

      // Resolve locals for the remaining states, but don't update any global state just
      // yet -- if anything fails to resolve the current state needs to remain untouched.
      // We also set up an inheritance chain for the locals here. This allows the view directive
      // to quickly look up the correct definition for each view in the current state. Even
      // though we create the locals object itself outside resolveState(), it is initially
      // empty and gets filled asynchronously. We need to keep track of the promise for the
      // (fully resolved) current locals, and pass this down the chain.
      var resolved = $q.when(locals);

      for (var l = keep; l < toPath.length; l++, state = toPath[l]) {
        locals = toLocals[l] = inherit(locals);
        resolved = resolveState(state, toParams, state === to, resolved, locals, options);
      }

      // Once everything is resolved, we are ready to perform the actual transition
      // and return a promise for the new state. We also keep track of what the
      // current promise is, so that we can detect overlapping transitions and
      // keep only the outcome of the last transition.
      var transition = $state.transition = resolved.then(function () {
        var l, entering, exiting;

        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        // Exit 'from' states not kept
        for (l = fromPath.length - 1; l >= keep; l--) {
          exiting = fromPath[l];
          if (exiting.self.onExit) {
            $injector.invoke(exiting.self.onExit, exiting.self, exiting.locals.globals);
          }
          exiting.locals = null;
        }

        // Enter 'to' states not kept
        for (l = keep; l < toPath.length; l++) {
          entering = toPath[l];
          entering.locals = toLocals[l];
          if (entering.self.onEnter) {
            $injector.invoke(entering.self.onEnter, entering.self, entering.locals.globals);
          }
        }

        // Run it again, to catch any transitions in callbacks
        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        // Update globals in $state
        $state.$current = to;
        $state.current = to.self;
        $state.params = toParams;
        copy($state.params, $stateParams);
        $state.transition = null;

        if (options.location && to.navigable) {
          $urlRouter.push(to.navigable.url, to.navigable.locals.globals.$stateParams, {
            $$avoidResync: true, replace: options.location === 'replace'
          });
        }

        if (options.notify) {
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeSuccess
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired once the state transition is **complete**.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         */
          $rootScope.$broadcast('$stateChangeSuccess', to.self, toParams, from.self, fromParams);
        }
        $urlRouter.update(true);

        return $state.current;
      }).then(null, function (error) {
        // propagate TransitionSuperseded error without emitting $stateChangeCancel
        // as it was already emitted in the success handler above
        if (error === TransitionSupersededError) return TransitionSuperseded;

        if ($state.transition !== transition) {
          $rootScope.$broadcast('$stateChangeCancel', to.self, toParams, from.self, fromParams);
          return TransitionSuperseded;
        }

        $state.transition = null;
        /**
         * @ngdoc event
         * @name ui.router.state.$state#$stateChangeError
         * @eventOf ui.router.state.$state
         * @eventType broadcast on root scope
         * @description
         * Fired when an **error occurs** during transition. It's important to note that if you
         * have any errors in your resolve functions (javascript errors, non-existent services, etc)
         * they will not throw traditionally. You must listen for this $stateChangeError event to
         * catch **ALL** errors.
         *
         * @param {Object} event Event object.
         * @param {State} toState The state being transitioned to.
         * @param {Object} toParams The params supplied to the `toState`.
         * @param {State} fromState The current state, pre-transition.
         * @param {Object} fromParams The params supplied to the `fromState`.
         * @param {Error} error The resolve error object.
         */
        evt = $rootScope.$broadcast('$stateChangeError', to.self, toParams, from.self, fromParams, error);

        if (!evt.defaultPrevented) {
          $urlRouter.update();
        }

        return $q.reject(error);
      });

      silenceUncaughtInPromise(transition);
      return transition;
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#is
     * @methodOf ui.router.state.$state
     *
     * @description
     * Similar to {@link ui.router.state.$state#methods_includes $state.includes},
     * but only checks for the full state name. If params is supplied then it will be
     * tested for strict equality against the current active params object, so all params
     * must match with none missing and no extras.
     *
     * @example
     * <pre>
     * $state.$current.name = 'contacts.details.item';
     *
     * // absolute name
     * $state.is('contact.details.item'); // returns true
     * $state.is(contactDetailItemStateObject); // returns true
     *
     * // relative name (. and ^), typically from a template
     * // E.g. from the 'contacts.details' template
     * <div ng-class="{highlighted: $state.is('.item')}">Item</div>
     * </pre>
     *
     * @param {string|object} stateOrName The state name (absolute or relative) or state object you'd like to check.
     * @param {object=} params A param object, e.g. `{sectionId: section.id}`, that you'd like
     * to test against the current active state.
     * @param {object=} options An options object.  The options are:
     *
     * - **`relative`** - {string|object} -  If `stateOrName` is a relative state name and `options.relative` is set, .is will
     * test relative to `options.relative` state (or name).
     *
     * @returns {boolean} Returns true if it is the state.
     */
    $state.is = function is(stateOrName, params, options) {
      options = extend({ relative: $state.$current }, options || {});
      var state = findState(stateOrName, options.relative);

      if (!isDefined(state)) { return undefined; }
      if ($state.$current !== state) { return false; }

      return !params || objectKeys(params).reduce(function(acc, key) {
        var paramDef = state.params[key];
        return acc && !paramDef || paramDef.type.equals($stateParams[key], params[key]);
      }, true);
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#includes
     * @methodOf ui.router.state.$state
     *
     * @description
     * A method to determine if the current active state is equal to or is the child of the
     * state stateName. If any params are passed then they will be tested for a match as well.
     * Not all the parameters need to be passed, just the ones you'd like to test for equality.
     *
     * @example
     * Partial and relative names
     * <pre>
     * $state.$current.name = 'contacts.details.item';
     *
     * // Using partial names
     * $state.includes("contacts"); // returns true
     * $state.includes("contacts.details"); // returns true
     * $state.includes("contacts.details.item"); // returns true
     * $state.includes("contacts.list"); // returns false
     * $state.includes("about"); // returns false
     *
     * // Using relative names (. and ^), typically from a template
     * // E.g. from the 'contacts.details' template
     * <div ng-class="{highlighted: $state.includes('.item')}">Item</div>
     * </pre>
     *
     * Basic globbing patterns
     * <pre>
     * $state.$current.name = 'contacts.details.item.url';
     *
     * $state.includes("*.details.*.*"); // returns true
     * $state.includes("*.details.**"); // returns true
     * $state.includes("**.item.**"); // returns true
     * $state.includes("*.details.item.url"); // returns true
     * $state.includes("*.details.*.url"); // returns true
     * $state.includes("*.details.*"); // returns false
     * $state.includes("item.**"); // returns false
     * </pre>
     *
     * @param {string} stateOrName A partial name, relative name, or glob pattern
     * to be searched for within the current state name.
     * @param {object=} params A param object, e.g. `{sectionId: section.id}`,
     * that you'd like to test against the current active state.
     * @param {object=} options An options object.  The options are:
     *
     * - **`relative`** - {string|object=} -  If `stateOrName` is a relative state reference and `options.relative` is set,
     * .includes will test relative to `options.relative` state (or name).
     *
     * @returns {boolean} Returns true if it does include the state
     */
    $state.includes = function includes(stateOrName, params, options) {
      options = extend({ relative: $state.$current }, options || {});
      if (isString(stateOrName) && isGlob(stateOrName)) {
        if (!doesStateMatchGlob(stateOrName)) {
          return false;
        }
        stateOrName = $state.$current.name;
      }

      var state = findState(stateOrName, options.relative);
      if (!isDefined(state)) { return undefined; }
      if (!isDefined($state.$current.includes[state.name])) { return false; }
      if (!params) { return true; }

      var keys = objectKeys(params);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i], paramDef = state.params[key];
        if (paramDef && !paramDef.type.equals($stateParams[key], params[key])) {
          return false;
        }
      }

      return objectKeys(params).reduce(function(acc, key) {
        var paramDef = state.params[key];
        return acc && !paramDef || paramDef.type.equals($stateParams[key], params[key]);
      }, true);
    };


    /**
     * @ngdoc function
     * @name ui.router.state.$state#href
     * @methodOf ui.router.state.$state
     *
     * @description
     * A url generation method that returns the compiled url for the given state populated with the given params.
     *
     * @example
     * <pre>
     * expect($state.href("about.person", { person: "bob" })).toEqual("/about/bob");
     * </pre>
     *
     * @param {string|object} stateOrName The state name or state object you'd like to generate a url from.
     * @param {object=} params An object of parameter values to fill the state's required parameters.
     * @param {object=} options Options object. The options are:
     *
     * - **`lossy`** - {boolean=true} -  If true, and if there is no url associated with the state provided in the
     *    first parameter, then the constructed href url will be built from the first navigable ancestor (aka
     *    ancestor with a valid url).
     * - **`inherit`** - {boolean=true}, If `true` will inherit url parameters from current url.
     * - **`relative`** - {object=$state.$current}, When transitioning with relative path (e.g '^'), 
     *    defines which state to be relative from.
     * - **`absolute`** - {boolean=false},  If true will generate an absolute url, e.g. "http://www.example.com/fullurl".
     * 
     * @returns {string} compiled state url
     */
    $state.href = function href(stateOrName, params, options) {
      options = extend({
        lossy:    true,
        inherit:  true,
        absolute: false,
        relative: $state.$current
      }, options || {});

      var state = findState(stateOrName, options.relative);

      if (!isDefined(state)) return null;
      if (options.inherit) params = inheritParams($stateParams, params || {}, $state.$current, state);
      
      var nav = (state && options.lossy) ? state.navigable : state;

      if (!nav || nav.url === undefined || nav.url === null) {
        return null;
      }
      return $urlRouter.href(nav.url, filterByKeys(state.params.$$keys().concat('#'), params || {}), {
        absolute: options.absolute
      });
    };

    /**
     * @ngdoc function
     * @name ui.router.state.$state#get
     * @methodOf ui.router.state.$state
     *
     * @description
     * Returns the state configuration object for any specific state or all states.
     *
     * @param {string|object=} stateOrName (absolute or relative) If provided, will only get the config for
     * the requested state. If not provided, returns an array of ALL state configs.
     * @param {string|object=} context When stateOrName is a relative state reference, the state will be retrieved relative to context.
     * @returns {Object|Array} State configuration object or array of all objects.
     */
    $state.get = function (stateOrName, context) {
      if (arguments.length === 0) return map(objectKeys(states), function(name) { return states[name].self; });
      var state = findState(stateOrName, context || $state.$current);
      return (state && state.self) ? state.self : null;
    };

    function resolveState(state, params, paramsAreFiltered, inherited, dst, options) {
      // Make a restricted $stateParams with only the parameters that apply to this state if
      // necessary. In addition to being available to the controller and onEnter/onExit callbacks,
      // we also need $stateParams to be available for any $injector calls we make during the
      // dependency resolution process.
      var $stateParams = (paramsAreFiltered) ? params : filterByKeys(state.params.$$keys(), params);
      var locals = { $stateParams: $stateParams };

      // Resolve 'global' dependencies for the state, i.e. those not specific to a view.
      // We're also including $stateParams in this; that way the parameters are restricted
      // to the set that should be visible to the state, and are independent of when we update
      // the global $state and $stateParams values.
      dst.resolve = $resolve.resolve(state.resolve, locals, dst.resolve, state);
      var promises = [dst.resolve.then(function (globals) {
        dst.globals = globals;
      })];
      if (inherited) promises.push(inherited);

      function resolveViews() {
        var viewsPromises = [];

        // Resolve template and dependencies for all views.
        forEach(state.views, function (view, name) {
          var injectables = (view.resolve && view.resolve !== state.resolve ? view.resolve : {});
          injectables.$template = [ function () {
            return $view.load(name, { view: view, locals: dst.globals, params: $stateParams, notify: options.notify }) || '';
          }];

          viewsPromises.push($resolve.resolve(injectables, dst.globals, dst.resolve, state).then(function (result) {
            // References to the controller (only instantiated at link time)
            if (isFunction(view.controllerProvider) || isArray(view.controllerProvider)) {
              var injectLocals = angular.extend({}, injectables, dst.globals);
              result.$$controller = $injector.invoke(view.controllerProvider, null, injectLocals);
            } else {
              result.$$controller = view.controller;
            }
            // Provide access to the state itself for internal use
            result.$$state = state;
            result.$$controllerAs = view.controllerAs;
            result.$$resolveAs = view.resolveAs;
            dst[name] = result;
          }));
        });

        return $q.all(viewsPromises).then(function(){
          return dst.globals;
        });
      }

      // Wait for all the promises and then return the activation object
      return $q.all(promises).then(resolveViews).then(function (values) {
        return dst;
      });
    }

    return $state;
  }

  function shouldSkipReload(to, toParams, from, fromParams, locals, options) {
    // Return true if there are no differences in non-search (path/object) params, false if there are differences
    function nonSearchParamsEqual(fromAndToState, fromParams, toParams) {
      // Identify whether all the parameters that differ between `fromParams` and `toParams` were search params.
      function notSearchParam(key) {
        return fromAndToState.params[key].location != "search";
      }
      var nonQueryParamKeys = fromAndToState.params.$$keys().filter(notSearchParam);
      var nonQueryParams = pick.apply({}, [fromAndToState.params].concat(nonQueryParamKeys));
      var nonQueryParamSet = new $$UMFP.ParamSet(nonQueryParams);
      return nonQueryParamSet.$$equals(fromParams, toParams);
    }

    // If reload was not explicitly requested
    // and we're transitioning to the same state we're already in
    // and    the locals didn't change
    //     or they changed in a way that doesn't merit reloading
    //        (reloadOnParams:false, or reloadOnSearch.false and only search params changed)
    // Then return true.
    if (!options.reload && to === from &&
      (locals === from.locals || (to.self.reloadOnSearch === false && nonSearchParamsEqual(from, fromParams, toParams)))) {
      return true;
    }
  }
}

angular.module('ui.router.state')
  .factory('$stateParams', function () { return {}; })
  .constant("$state.runtime", { autoinject: true })
  .provider('$state', $StateProvider)
  // Inject $state to initialize when entering runtime. #2574
  .run(['$injector', function ($injector) {
    // Allow tests (stateSpec.js) to turn this off by defining this constant
    if ($injector.get("$state.runtime").autoinject) {
      $injector.get('$state');
    }
  }]);


$ViewProvider.$inject = [];
function $ViewProvider() {

  this.$get = $get;
  /**
   * @ngdoc object
   * @name ui.router.state.$view
   *
   * @requires ui.router.util.$templateFactory
   * @requires $rootScope
   *
   * @description
   *
   */
  $get.$inject = ['$rootScope', '$templateFactory'];
  function $get(   $rootScope,   $templateFactory) {
    return {
      // $view.load('full.viewName', { template: ..., controller: ..., resolve: ..., async: false, params: ... })
      /**
       * @ngdoc function
       * @name ui.router.state.$view#load
       * @methodOf ui.router.state.$view
       *
       * @description
       *
       * @param {string} name name
       * @param {object} options option object.
       */
      load: function load(name, options) {
        var result, defaults = {
          template: null, controller: null, view: null, locals: null, notify: true, async: true, params: {}
        };
        options = extend(defaults, options);

        if (options.view) {
          result = $templateFactory.fromConfig(options.view, options.params, options.locals);
        }
        return result;
      }
    };
  }
}

angular.module('ui.router.state').provider('$view', $ViewProvider);

/**
 * @ngdoc object
 * @name ui.router.state.$uiViewScrollProvider
 *
 * @description
 * Provider that returns the {@link ui.router.state.$uiViewScroll} service function.
 */
function $ViewScrollProvider() {

  var useAnchorScroll = false;

  /**
   * @ngdoc function
   * @name ui.router.state.$uiViewScrollProvider#useAnchorScroll
   * @methodOf ui.router.state.$uiViewScrollProvider
   *
   * @description
   * Reverts back to using the core [`$anchorScroll`](http://docs.angularjs.org/api/ng.$anchorScroll) service for
   * scrolling based on the url anchor.
   */
  this.useAnchorScroll = function () {
    useAnchorScroll = true;
  };

  /**
   * @ngdoc object
   * @name ui.router.state.$uiViewScroll
   *
   * @requires $anchorScroll
   * @requires $timeout
   *
   * @description
   * When called with a jqLite element, it scrolls the element into view (after a
   * `$timeout` so the DOM has time to refresh).
   *
   * If you prefer to rely on `$anchorScroll` to scroll the view to the anchor,
   * this can be enabled by calling {@link ui.router.state.$uiViewScrollProvider#methods_useAnchorScroll `$uiViewScrollProvider.useAnchorScroll()`}.
   */
  this.$get = ['$anchorScroll', '$timeout', function ($anchorScroll, $timeout) {
    if (useAnchorScroll) {
      return $anchorScroll;
    }

    return function ($element) {
      return $timeout(function () {
        $element[0].scrollIntoView();
      }, 0, false);
    };
  }];
}

angular.module('ui.router.state').provider('$uiViewScroll', $ViewScrollProvider);

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-view
 *
 * @requires ui.router.state.$state
 * @requires $compile
 * @requires $controller
 * @requires $injector
 * @requires ui.router.state.$uiViewScroll
 * @requires $document
 *
 * @restrict ECA
 *
 * @description
 * The ui-view directive tells $state where to place your templates.
 *
 * @param {string=} name A view name. The name should be unique amongst the other views in the
 * same state. You can have views of the same name that live in different states.
 *
 * @param {string=} autoscroll It allows you to set the scroll behavior of the browser window
 * when a view is populated. By default, $anchorScroll is overridden by ui-router's custom scroll
 * service, {@link ui.router.state.$uiViewScroll}. This custom service let's you
 * scroll ui-view elements into view when they are populated during a state activation.
 *
 * *Note: To revert back to old [`$anchorScroll`](http://docs.angularjs.org/api/ng.$anchorScroll)
 * functionality, call `$uiViewScrollProvider.useAnchorScroll()`.*
 *
 * @param {string=} onload Expression to evaluate whenever the view updates.
 *
 * @example
 * A view can be unnamed or named.
 * <pre>
 * <!-- Unnamed -->
 * <div ui-view></div>
 *
 * <!-- Named -->
 * <div ui-view="viewName"></div>
 * </pre>
 *
 * You can only have one unnamed view within any template (or root html). If you are only using a
 * single view and it is unnamed then you can populate it like so:
 * <pre>
 * <div ui-view></div>
 * $stateProvider.state("home", {
 *   template: "<h1>HELLO!</h1>"
 * })
 * </pre>
 *
 * The above is a convenient shortcut equivalent to specifying your view explicitly with the {@link ui.router.state.$stateProvider#methods_state `views`}
 * config property, by name, in this case an empty name:
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "": {
 *       template: "<h1>HELLO!</h1>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * But typically you'll only use the views property if you name your view or have more than one view
 * in the same template. There's not really a compelling reason to name a view if its the only one,
 * but you could if you wanted, like so:
 * <pre>
 * <div ui-view="main"></div>
 * </pre>
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "main": {
 *       template: "<h1>HELLO!</h1>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * Really though, you'll use views to set up multiple views:
 * <pre>
 * <div ui-view></div>
 * <div ui-view="chart"></div>
 * <div ui-view="data"></div>
 * </pre>
 *
 * <pre>
 * $stateProvider.state("home", {
 *   views: {
 *     "": {
 *       template: "<h1>HELLO!</h1>"
 *     },
 *     "chart": {
 *       template: "<chart_thing/>"
 *     },
 *     "data": {
 *       template: "<data_thing/>"
 *     }
 *   }    
 * })
 * </pre>
 *
 * Examples for `autoscroll`:
 *
 * <pre>
 * <!-- If autoscroll present with no expression,
 *      then scroll ui-view into view -->
 * <ui-view autoscroll/>
 *
 * <!-- If autoscroll present with valid expression,
 *      then scroll ui-view into view if expression evaluates to true -->
 * <ui-view autoscroll='true'/>
 * <ui-view autoscroll='false'/>
 * <ui-view autoscroll='scopeVariable'/>
 * </pre>
 *
 * Resolve data:
 *
 * The resolved data from the state's `resolve` block is placed on the scope as `$resolve` (this
 * can be customized using [[ViewDeclaration.resolveAs]]).  This can be then accessed from the template.
 *
 * Note that when `controllerAs` is being used, `$resolve` is set on the controller instance *after* the
 * controller is instantiated.  The `$onInit()` hook can be used to perform initialization code which
 * depends on `$resolve` data.
 *
 * Example usage of $resolve in a view template
 * <pre>
 * $stateProvider.state('home', {
 *   template: '<my-component user="$resolve.user"></my-component>',
 *   resolve: {
 *     user: function(UserService) { return UserService.fetchUser(); }
 *   }
 * });
 * </pre>
 */
$ViewDirective.$inject = ['$state', '$injector', '$uiViewScroll', '$interpolate', '$q'];
function $ViewDirective(   $state,   $injector,   $uiViewScroll,   $interpolate,   $q) {

  function getService() {
    return ($injector.has) ? function(service) {
      return $injector.has(service) ? $injector.get(service) : null;
    } : function(service) {
      try {
        return $injector.get(service);
      } catch (e) {
        return null;
      }
    };
  }

  var service = getService(),
      $animator = service('$animator'),
      $animate = service('$animate');

  // Returns a set of DOM manipulation functions based on which Angular version
  // it should use
  function getRenderer(attrs, scope) {
    var statics = function() {
      return {
        enter: function (element, target, cb) { target.after(element); cb(); },
        leave: function (element, cb) { element.remove(); cb(); }
      };
    };

    if ($animate) {
      return {
        enter: function(element, target, cb) {
          if (angular.version.minor > 2) {
            $animate.enter(element, null, target).then(cb);
          } else {
            $animate.enter(element, null, target, cb);
          }
        },
        leave: function(element, cb) {
          if (angular.version.minor > 2) {
            $animate.leave(element).then(cb);
          } else {
            $animate.leave(element, cb);
          }
        }
      };
    }

    if ($animator) {
      var animate = $animator && $animator(scope, attrs);

      return {
        enter: function(element, target, cb) {animate.enter(element, null, target); cb(); },
        leave: function(element, cb) { animate.leave(element); cb(); }
      };
    }

    return statics();
  }

  var directive = {
    restrict: 'ECA',
    terminal: true,
    priority: 400,
    transclude: 'element',
    compile: function (tElement, tAttrs, $transclude) {
      return function (scope, $element, attrs) {
        var previousEl, currentEl, currentScope, latestLocals,
            onloadExp     = attrs.onload || '',
            autoScrollExp = attrs.autoscroll,
            renderer      = getRenderer(attrs, scope),
            inherited     = $element.inheritedData('$uiView');

        scope.$on('$stateChangeSuccess', function() {
          updateView(false);
        });

        updateView(true);

        function cleanupLastView() {
          if (previousEl) {
            previousEl.remove();
            previousEl = null;
          }

          if (currentScope) {
            currentScope.$destroy();
            currentScope = null;
          }

          if (currentEl) {
            var $uiViewData = currentEl.data('$uiViewAnim');
            renderer.leave(currentEl, function() {
              $uiViewData.$$animLeave.resolve();
              previousEl = null;
            });

            previousEl = currentEl;
            currentEl = null;
          }
        }

        function updateView(firstTime) {
          var newScope,
              name            = getUiViewName(scope, attrs, $element, $interpolate),
              previousLocals  = name && $state.$current && $state.$current.locals[name];

          if (!firstTime && previousLocals === latestLocals) return; // nothing to do
          newScope = scope.$new();
          latestLocals = $state.$current.locals[name];

          /**
           * @ngdoc event
           * @name ui.router.state.directive:ui-view#$viewContentLoading
           * @eventOf ui.router.state.directive:ui-view
           * @eventType emits on ui-view directive scope
           * @description
           *
           * Fired once the view **begins loading**, *before* the DOM is rendered.
           *
           * @param {Object} event Event object.
           * @param {string} viewName Name of the view.
           */
          newScope.$emit('$viewContentLoading', name);

          var clone = $transclude(newScope, function(clone) {
            var animEnter = $q.defer(), animLeave = $q.defer();
            var viewAnimData = {
              $animEnter: animEnter.promise,
              $animLeave: animLeave.promise,
              $$animLeave: animLeave
            };

            clone.data('$uiViewAnim', viewAnimData);
            renderer.enter(clone, $element, function onUiViewEnter() {
              animEnter.resolve();
              if(currentScope) {
                currentScope.$emit('$viewContentAnimationEnded');
              }

              if (angular.isDefined(autoScrollExp) && !autoScrollExp || scope.$eval(autoScrollExp)) {
                $uiViewScroll(clone);
              }
            });
            cleanupLastView();
          });

          currentEl = clone;
          currentScope = newScope;
          /**
           * @ngdoc event
           * @name ui.router.state.directive:ui-view#$viewContentLoaded
           * @eventOf ui.router.state.directive:ui-view
           * @eventType emits on ui-view directive scope
           * @description
           * Fired once the view is **loaded**, *after* the DOM is rendered.
           *
           * @param {Object} event Event object.
           * @param {string} viewName Name of the view.
           */
          currentScope.$emit('$viewContentLoaded', name);
          currentScope.$eval(onloadExp);
        }
      };
    }
  };

  return directive;
}

$ViewDirectiveFill.$inject = ['$compile', '$controller', '$state', '$interpolate'];
function $ViewDirectiveFill (  $compile,   $controller,   $state,   $interpolate) {
  return {
    restrict: 'ECA',
    priority: -400,
    compile: function (tElement) {
      var initial = tElement.html();
      if (tElement.empty) {
        tElement.empty();
      } else {
        // ng 1.0.0 doesn't have empty(), which cleans up data and handlers
        tElement[0].innerHTML = null;
      }

      return function (scope, $element, attrs) {
        var current = $state.$current,
            name = getUiViewName(scope, attrs, $element, $interpolate),
            locals  = current && current.locals[name];

        if (! locals) {
          $element.html(initial);
          $compile($element.contents())(scope);
          return;
        }

        $element.data('$uiView', { name: name, state: locals.$$state });
        $element.html(locals.$template ? locals.$template : initial);

        var resolveData = angular.extend({}, locals);
        scope[locals.$$resolveAs] = resolveData;

        var link = $compile($element.contents());

        if (locals.$$controller) {
          locals.$scope = scope;
          locals.$element = $element;
          var controller = $controller(locals.$$controller, locals);
          if (locals.$$controllerAs) {
            scope[locals.$$controllerAs] = controller;
            scope[locals.$$controllerAs][locals.$$resolveAs] = resolveData;
          }
          if (isFunction(controller.$onInit)) controller.$onInit();
          $element.data('$ngControllerController', controller);
          $element.children().data('$ngControllerController', controller);
        }

        link(scope);
      };
    }
  };
}

/**
 * Shared ui-view code for both directives:
 * Given scope, element, and its attributes, return the view's name
 */
function getUiViewName(scope, attrs, element, $interpolate) {
  var name = $interpolate(attrs.uiView || attrs.name || '')(scope);
  var uiViewCreatedBy = element.inheritedData('$uiView');
  return name.indexOf('@') >= 0 ?  name :  (name + '@' + (uiViewCreatedBy ? uiViewCreatedBy.state.name : ''));
}

angular.module('ui.router.state').directive('uiView', $ViewDirective);
angular.module('ui.router.state').directive('uiView', $ViewDirectiveFill);

function parseStateRef(ref, current) {
  var preparsed = ref.match(/^\s*({[^}]*})\s*$/), parsed;
  if (preparsed) ref = current + '(' + preparsed[1] + ')';
  parsed = ref.replace(/\n/g, " ").match(/^([^(]+?)\s*(\((.*)\))?$/);
  if (!parsed || parsed.length !== 4) throw new Error("Invalid state ref '" + ref + "'");
  return { state: parsed[1], paramExpr: parsed[3] || null };
}

function stateContext(el) {
  var stateData = el.parent().inheritedData('$uiView');

  if (stateData && stateData.state && stateData.state.name) {
    return stateData.state;
  }
}

function getTypeInfo(el) {
  // SVGAElement does not use the href attribute, but rather the 'xlinkHref' attribute.
  var isSvg = Object.prototype.toString.call(el.prop('href')) === '[object SVGAnimatedString]';
  var isForm = el[0].nodeName === "FORM";

  return {
    attr: isForm ? "action" : (isSvg ? 'xlink:href' : 'href'),
    isAnchor: el.prop("tagName").toUpperCase() === "A",
    clickable: !isForm
  };
}

function clickHook(el, $state, $timeout, type, current) {
  return function(e) {
    var button = e.which || e.button, target = current();

    if (!(button > 1 || e.ctrlKey || e.metaKey || e.shiftKey || el.attr('target'))) {
      // HACK: This is to allow ng-clicks to be processed before the transition is initiated:
      var transition = $timeout(function() {
        $state.go(target.state, target.params, target.options);
      });
      e.preventDefault();

      // if the state has no URL, ignore one preventDefault from the <a> directive.
      var ignorePreventDefaultCount = type.isAnchor && !target.href ? 1: 0;

      e.preventDefault = function() {
        if (ignorePreventDefaultCount-- <= 0) $timeout.cancel(transition);
      };
    }
  };
}

function defaultOpts(el, $state) {
  return { relative: stateContext(el) || $state.$current, inherit: true };
}

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref
 *
 * @requires ui.router.state.$state
 * @requires $timeout
 *
 * @restrict A
 *
 * @description
 * A directive that binds a link (`<a>` tag) to a state. If the state has an associated
 * URL, the directive will automatically generate & update the `href` attribute via
 * the {@link ui.router.state.$state#methods_href $state.href()} method. Clicking
 * the link will trigger a state transition with optional parameters.
 *
 * Also middle-clicking, right-clicking, and ctrl-clicking on the link will be
 * handled natively by the browser.
 *
 * You can also use relative state paths within ui-sref, just like the relative
 * paths passed to `$state.go()`. You just need to be aware that the path is relative
 * to the state that the link lives in, in other words the state that loaded the
 * template containing the link.
 *
 * You can specify options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 * using the `ui-sref-opts` attribute. Options are restricted to `location`, `inherit`,
 * and `reload`.
 *
 * @example
 * Here's an example of how you'd use ui-sref and how it would compile. If you have the
 * following template:
 * <pre>
 * <a ui-sref="home">Home</a> | <a ui-sref="about">About</a> | <a ui-sref="{page: 2}">Next page</a>
 *
 * <ul>
 *     <li ng-repeat="contact in contacts">
 *         <a ui-sref="contacts.detail({ id: contact.id })">{{ contact.name }}</a>
 *     </li>
 * </ul>
 * </pre>
 *
 * Then the compiled html would be (assuming Html5Mode is off and current state is contacts):
 * <pre>
 * <a href="#/home" ui-sref="home">Home</a> | <a href="#/about" ui-sref="about">About</a> | <a href="#/contacts?page=2" ui-sref="{page: 2}">Next page</a>
 *
 * <ul>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/1" ui-sref="contacts.detail({ id: contact.id })">Joe</a>
 *     </li>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/2" ui-sref="contacts.detail({ id: contact.id })">Alice</a>
 *     </li>
 *     <li ng-repeat="contact in contacts">
 *         <a href="#/contacts/3" ui-sref="contacts.detail({ id: contact.id })">Bob</a>
 *     </li>
 * </ul>
 *
 * <a ui-sref="home" ui-sref-opts="{reload: true}">Home</a>
 * </pre>
 *
 * @param {string} ui-sref 'stateName' can be any valid absolute or relative state
 * @param {Object} ui-sref-opts options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 */
$StateRefDirective.$inject = ['$state', '$timeout'];
function $StateRefDirective($state, $timeout) {
  return {
    restrict: 'A',
    require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
    link: function(scope, element, attrs, uiSrefActive) {
      var ref    = parseStateRef(attrs.uiSref, $state.current.name);
      var def    = { state: ref.state, href: null, params: null };
      var type   = getTypeInfo(element);
      var active = uiSrefActive[1] || uiSrefActive[0];
      var unlinkInfoFn = null;
      var hookFn;

      def.options = extend(defaultOpts(element, $state), attrs.uiSrefOpts ? scope.$eval(attrs.uiSrefOpts) : {});

      var update = function(val) {
        if (val) def.params = angular.copy(val);
        def.href = $state.href(ref.state, def.params, def.options);

        if (unlinkInfoFn) unlinkInfoFn();
        if (active) unlinkInfoFn = active.$$addStateInfo(ref.state, def.params);
        if (def.href !== null) attrs.$set(type.attr, def.href);
      };

      if (ref.paramExpr) {
        scope.$watch(ref.paramExpr, function(val) { if (val !== def.params) update(val); }, true);
        def.params = angular.copy(scope.$eval(ref.paramExpr));
      }
      update();

      if (!type.clickable) return;
      hookFn = clickHook(element, $state, $timeout, type, function() { return def; });
      element[element.on ? 'on' : 'bind']("click", hookFn);
      scope.$on('$destroy', function() {
        element[element.off ? 'off' : 'unbind']("click", hookFn);
      });
    }
  };
}

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-state
 *
 * @requires ui.router.state.uiSref
 *
 * @restrict A
 *
 * @description
 * Much like ui-sref, but will accept named $scope properties to evaluate for a state definition,
 * params and override options.
 *
 * @param {string} ui-state 'stateName' can be any valid absolute or relative state
 * @param {Object} ui-state-params params to pass to {@link ui.router.state.$state#methods_href $state.href()}
 * @param {Object} ui-state-opts options to pass to {@link ui.router.state.$state#methods_go $state.go()}
 */
$StateRefDynamicDirective.$inject = ['$state', '$timeout'];
function $StateRefDynamicDirective($state, $timeout) {
  return {
    restrict: 'A',
    require: ['?^uiSrefActive', '?^uiSrefActiveEq'],
    link: function(scope, element, attrs, uiSrefActive) {
      var type   = getTypeInfo(element);
      var active = uiSrefActive[1] || uiSrefActive[0];
      var group  = [attrs.uiState, attrs.uiStateParams || null, attrs.uiStateOpts || null];
      var watch  = '[' + group.map(function(val) { return val || 'null'; }).join(', ') + ']';
      var def    = { state: null, params: null, options: null, href: null };
      var unlinkInfoFn = null;
      var hookFn;

      function runStateRefLink (group) {
        def.state = group[0]; def.params = group[1]; def.options = group[2];
        def.href = $state.href(def.state, def.params, def.options);

        if (unlinkInfoFn) unlinkInfoFn();
        if (active) unlinkInfoFn = active.$$addStateInfo(def.state, def.params);
        if (def.href) attrs.$set(type.attr, def.href);
      }

      scope.$watch(watch, runStateRefLink, true);
      runStateRefLink(scope.$eval(watch));

      if (!type.clickable) return;
      hookFn = clickHook(element, $state, $timeout, type, function() { return def; });
      element[element.on ? 'on' : 'bind']("click", hookFn);
      scope.$on('$destroy', function() {
        element[element.off ? 'off' : 'unbind']("click", hookFn);
      });
    }
  };
}


/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref-active
 *
 * @requires ui.router.state.$state
 * @requires ui.router.state.$stateParams
 * @requires $interpolate
 *
 * @restrict A
 *
 * @description
 * A directive working alongside ui-sref to add classes to an element when the
 * related ui-sref directive's state is active, and removing them when it is inactive.
 * The primary use-case is to simplify the special appearance of navigation menus
 * relying on `ui-sref`, by having the "active" state's menu button appear different,
 * distinguishing it from the inactive menu items.
 *
 * ui-sref-active can live on the same element as ui-sref or on a parent element. The first
 * ui-sref-active found at the same level or above the ui-sref will be used.
 *
 * Will activate when the ui-sref's target state or any child state is active. If you
 * need to activate only when the ui-sref target state is active and *not* any of
 * it's children, then you will use
 * {@link ui.router.state.directive:ui-sref-active-eq ui-sref-active-eq}
 *
 * @example
 * Given the following template:
 * <pre>
 * <ul>
 *   <li ui-sref-active="active" class="item">
 *     <a href ui-sref="app.user({user: 'bilbobaggins'})">@bilbobaggins</a>
 *   </li>
 * </ul>
 * </pre>
 *
 *
 * When the app state is "app.user" (or any children states), and contains the state parameter "user" with value "bilbobaggins",
 * the resulting HTML will appear as (note the 'active' class):
 * <pre>
 * <ul>
 *   <li ui-sref-active="active" class="item active">
 *     <a ui-sref="app.user({user: 'bilbobaggins'})" href="/users/bilbobaggins">@bilbobaggins</a>
 *   </li>
 * </ul>
 * </pre>
 *
 * The class name is interpolated **once** during the directives link time (any further changes to the
 * interpolated value are ignored).
 *
 * Multiple classes may be specified in a space-separated format:
 * <pre>
 * <ul>
 *   <li ui-sref-active='class1 class2 class3'>
 *     <a ui-sref="app.user">link</a>
 *   </li>
 * </ul>
 * </pre>
 *
 * It is also possible to pass ui-sref-active an expression that evaluates
 * to an object hash, whose keys represent active class names and whose
 * values represent the respective state names/globs.
 * ui-sref-active will match if the current active state **includes** any of
 * the specified state names/globs, even the abstract ones.
 *
 * @Example
 * Given the following template, with "admin" being an abstract state:
 * <pre>
 * <div ui-sref-active="{'active': 'admin.*'}">
 *   <a ui-sref-active="active" ui-sref="admin.roles">Roles</a>
 * </div>
 * </pre>
 *
 * When the current state is "admin.roles" the "active" class will be applied
 * to both the <div> and <a> elements. It is important to note that the state
 * names/globs passed to ui-sref-active shadow the state provided by ui-sref.
 */

/**
 * @ngdoc directive
 * @name ui.router.state.directive:ui-sref-active-eq
 *
 * @requires ui.router.state.$state
 * @requires ui.router.state.$stateParams
 * @requires $interpolate
 *
 * @restrict A
 *
 * @description
 * The same as {@link ui.router.state.directive:ui-sref-active ui-sref-active} but will only activate
 * when the exact target state used in the `ui-sref` is active; no child states.
 *
 */
$StateRefActiveDirective.$inject = ['$state', '$stateParams', '$interpolate'];
function $StateRefActiveDirective($state, $stateParams, $interpolate) {
  return  {
    restrict: "A",
    controller: ['$scope', '$element', '$attrs', '$timeout', function ($scope, $element, $attrs, $timeout) {
      var states = [], activeClasses = {}, activeEqClass, uiSrefActive;

      // There probably isn't much point in $observing this
      // uiSrefActive and uiSrefActiveEq share the same directive object with some
      // slight difference in logic routing
      activeEqClass = $interpolate($attrs.uiSrefActiveEq || '', false)($scope);

      try {
        uiSrefActive = $scope.$eval($attrs.uiSrefActive);
      } catch (e) {
        // Do nothing. uiSrefActive is not a valid expression.
        // Fall back to using $interpolate below
      }
      uiSrefActive = uiSrefActive || $interpolate($attrs.uiSrefActive || '', false)($scope);
      if (isObject(uiSrefActive)) {
        forEach(uiSrefActive, function(stateOrName, activeClass) {
          if (isString(stateOrName)) {
            var ref = parseStateRef(stateOrName, $state.current.name);
            addState(ref.state, $scope.$eval(ref.paramExpr), activeClass);
          }
        });
      }

      // Allow uiSref to communicate with uiSrefActive[Equals]
      this.$$addStateInfo = function (newState, newParams) {
        // we already got an explicit state provided by ui-sref-active, so we
        // shadow the one that comes from ui-sref
        if (isObject(uiSrefActive) && states.length > 0) {
          return;
        }
        var deregister = addState(newState, newParams, uiSrefActive);
        update();
        return deregister;
      };

      $scope.$on('$stateChangeSuccess', update);

      function addState(stateName, stateParams, activeClass) {
        var state = $state.get(stateName, stateContext($element));
        var stateHash = createStateHash(stateName, stateParams);

        var stateInfo = {
          state: state || { name: stateName },
          params: stateParams,
          hash: stateHash
        };

        states.push(stateInfo);
        activeClasses[stateHash] = activeClass;

        return function removeState() {
          var idx = states.indexOf(stateInfo);
          if (idx !== -1) states.splice(idx, 1);
        };
      }

      /**
       * @param {string} state
       * @param {Object|string} [params]
       * @return {string}
       */
      function createStateHash(state, params) {
        if (!isString(state)) {
          throw new Error('state should be a string');
        }
        if (isObject(params)) {
          return state + toJson(params);
        }
        params = $scope.$eval(params);
        if (isObject(params)) {
          return state + toJson(params);
        }
        return state;
      }

      // Update route state
      function update() {
        for (var i = 0; i < states.length; i++) {
          if (anyMatch(states[i].state, states[i].params)) {
            addClass($element, activeClasses[states[i].hash]);
          } else {
            removeClass($element, activeClasses[states[i].hash]);
          }

          if (exactMatch(states[i].state, states[i].params)) {
            addClass($element, activeEqClass);
          } else {
            removeClass($element, activeEqClass);
          }
        }
      }

      function addClass(el, className) { $timeout(function () { el.addClass(className); }); }
      function removeClass(el, className) { el.removeClass(className); }
      function anyMatch(state, params) { return $state.includes(state.name, params); }
      function exactMatch(state, params) { return $state.is(state.name, params); }

      update();
    }]
  };
}

angular.module('ui.router.state')
  .directive('uiSref', $StateRefDirective)
  .directive('uiSrefActive', $StateRefActiveDirective)
  .directive('uiSrefActiveEq', $StateRefActiveDirective)
  .directive('uiState', $StateRefDynamicDirective);

/**
 * @ngdoc filter
 * @name ui.router.state.filter:isState
 *
 * @requires ui.router.state.$state
 *
 * @description
 * Translates to {@link ui.router.state.$state#methods_is $state.is("stateName")}.
 */
$IsStateFilter.$inject = ['$state'];
function $IsStateFilter($state) {
  var isFilter = function (state, params) {
    return $state.is(state, params);
  };
  isFilter.$stateful = true;
  return isFilter;
}

/**
 * @ngdoc filter
 * @name ui.router.state.filter:includedByState
 *
 * @requires ui.router.state.$state
 *
 * @description
 * Translates to {@link ui.router.state.$state#methods_includes $state.includes('fullOrPartialStateName')}.
 */
$IncludedByStateFilter.$inject = ['$state'];
function $IncludedByStateFilter($state) {
  var includesFilter = function (state, params, options) {
    return $state.includes(state, params, options);
  };
  includesFilter.$stateful = true;
  return  includesFilter;
}

angular.module('ui.router.state')
  .filter('isState', $IsStateFilter)
  .filter('includedByState', $IncludedByStateFilter);
})(window, window.angular);
/*
 * angular-mm-foundation
 * http://pineconellc.github.io/angular-foundation/

 * Version: 0.8.0 - 2015-10-13
 * License: MIT
 * (c) Pinecone, LLC
 */
angular.module("mm.foundation", ["mm.foundation.tpls", "mm.foundation.accordion","mm.foundation.alert","mm.foundation.bindHtml","mm.foundation.buttons","mm.foundation.position","mm.foundation.mediaQueries","mm.foundation.dropdownToggle","mm.foundation.interchange","mm.foundation.transition","mm.foundation.modal","mm.foundation.offcanvas","mm.foundation.pagination","mm.foundation.tooltip","mm.foundation.popover","mm.foundation.progressbar","mm.foundation.rating","mm.foundation.tabs","mm.foundation.topbar","mm.foundation.tour","mm.foundation.typeahead"]);
angular.module("mm.foundation.tpls", ["template/accordion/accordion-group.html","template/accordion/accordion.html","template/alert/alert.html","template/modal/backdrop.html","template/modal/window.html","template/pagination/pager.html","template/pagination/pagination.html","template/tooltip/tooltip-html-unsafe-popup.html","template/tooltip/tooltip-popup.html","template/popover/popover.html","template/progressbar/bar.html","template/progressbar/progress.html","template/progressbar/progressbar.html","template/rating/rating.html","template/tabs/tab.html","template/tabs/tabset.html","template/topbar/has-dropdown.html","template/topbar/toggle-top-bar.html","template/topbar/top-bar-dropdown.html","template/topbar/top-bar-section.html","template/topbar/top-bar.html","template/tour/tour.html","template/typeahead/typeahead-match.html","template/typeahead/typeahead-popup.html"]);
angular.module('mm.foundation.accordion', [])

.constant('accordionConfig', {
  closeOthers: true
})

.controller('AccordionController', ['$scope', '$attrs', 'accordionConfig', function ($scope, $attrs, accordionConfig) {

  // This array keeps track of the accordion groups
  this.groups = [];

  // Ensure that all the groups in this accordion are closed, unless close-others explicitly says not to
  this.closeOthers = function(openGroup) {
    var closeOthers = angular.isDefined($attrs.closeOthers) ? $scope.$eval($attrs.closeOthers) : accordionConfig.closeOthers;
    if ( closeOthers ) {
      angular.forEach(this.groups, function (group) {
        if ( group !== openGroup ) {
          group.isOpen = false;
        }
      });
    }
  };
  
  // This is called from the accordion-group directive to add itself to the accordion
  this.addGroup = function(groupScope) {
    var that = this;
    this.groups.push(groupScope);

    groupScope.$on('$destroy', function (event) {
      that.removeGroup(groupScope);
    });
  };

  // This is called from the accordion-group directive when to remove itself
  this.removeGroup = function(group) {
    var index = this.groups.indexOf(group);
    if ( index !== -1 ) {
      this.groups.splice(index, 1);
    }
  };

}])

// The accordion directive simply sets up the directive controller
// and adds an accordion CSS class to itself element.
.directive('accordion', function () {
  return {
    restrict:'EA',
    controller:'AccordionController',
    transclude: true,
    replace: false,
    templateUrl: 'template/accordion/accordion.html'
  };
})

// The accordion-group directive indicates a block of html that will expand and collapse in an accordion
.directive('accordionGroup', ['$parse', function($parse) {
  return {
    require:'^accordion',         // We need this directive to be inside an accordion
    restrict:'EA',
    transclude:true,              // It transcludes the contents of the directive into the template
    replace: true,                // The element containing the directive will be replaced with the template
    templateUrl:'template/accordion/accordion-group.html',
    scope:{ heading:'@' },        // Create an isolated scope and interpolate the heading attribute onto this scope
    controller: function() {
      this.setHeading = function(element) {
        this.heading = element;
      };
    },
    link: function(scope, element, attrs, accordionCtrl) {
      var getIsOpen, setIsOpen;

      accordionCtrl.addGroup(scope);

      scope.isOpen = false;
      
      if ( attrs.isOpen ) {
        getIsOpen = $parse(attrs.isOpen);
        setIsOpen = getIsOpen.assign;

        scope.$parent.$watch(getIsOpen, function(value) {
          scope.isOpen = !!value;
        });
      }

      scope.$watch('isOpen', function(value) {
        if ( value ) {
          accordionCtrl.closeOthers(scope);
        }
        if ( setIsOpen ) {
          setIsOpen(scope.$parent, value);
        }
      });
    }
  };
}])

// Use accordion-heading below an accordion-group to provide a heading containing HTML
// <accordion-group>
//   <accordion-heading>Heading containing HTML - <img src="..."></accordion-heading>
// </accordion-group>
.directive('accordionHeading', function() {
  return {
    restrict: 'EA',
    transclude: true,   // Grab the contents to be used as the heading
    template: '',       // In effect remove this element!
    replace: true,
    require: '^accordionGroup',
    compile: function(element, attr, transclude) {
      return function link(scope, element, attr, accordionGroupCtrl) {
        // Pass the heading to the accordion-group controller
        // so that it can be transcluded into the right place in the template
        // [The second parameter to transclude causes the elements to be cloned so that they work in ng-repeat]
        accordionGroupCtrl.setHeading(transclude(scope, function() {}));
      };
    }
  };
})

// Use in the accordion-group template to indicate where you want the heading to be transcluded
// You must provide the property on the accordion-group controller that will hold the transcluded element
// <div class="accordion-group">
//   <div class="accordion-heading" ><a ... accordion-transclude="heading">...</a></div>
//   ...
// </div>
.directive('accordionTransclude', function() {
  return {
    require: '^accordionGroup',
    link: function(scope, element, attr, controller) {
      scope.$watch(function() { return controller[attr.accordionTransclude]; }, function(heading) {
        if ( heading ) {
          element.html('');
          element.append(heading);
        }
      });
    }
  };
});

angular.module("mm.foundation.alert", [])

.controller('AlertController', ['$scope', '$attrs', function ($scope, $attrs) {
  $scope.closeable = 'close' in $attrs && typeof $attrs.close !== "undefined";
}])

.directive('alert', function () {
  return {
    restrict:'EA',
    controller:'AlertController',
    templateUrl:'template/alert/alert.html',
    transclude:true,
    replace:true,
    scope: {
      type: '=',
      close: '&'
    }
  };
});

angular.module('mm.foundation.bindHtml', [])

  .directive('bindHtmlUnsafe', function () {
    return function (scope, element, attr) {
      element.addClass('ng-binding').data('$binding', attr.bindHtmlUnsafe);
      scope.$watch(attr.bindHtmlUnsafe, function bindHtmlUnsafeWatchAction(value) {
        element.html(value || '');
      });
    };
  });

angular.module('mm.foundation.buttons', [])

.constant('buttonConfig', {
  activeClass: 'active',
  toggleEvent: 'click'
})

.controller('ButtonsController', ['buttonConfig', function(buttonConfig) {
  this.activeClass = buttonConfig.activeClass;
  this.toggleEvent = buttonConfig.toggleEvent;
}])

.directive('btnRadio', function () {
  return {
    require: ['btnRadio', 'ngModel'],
    controller: 'ButtonsController',
    link: function (scope, element, attrs, ctrls) {
      var buttonsCtrl = ctrls[0], ngModelCtrl = ctrls[1];

      //model -> UI
      ngModelCtrl.$render = function () {
        element.toggleClass(buttonsCtrl.activeClass, angular.equals(ngModelCtrl.$modelValue, scope.$eval(attrs.btnRadio)));
      };

      //ui->model
      element.bind(buttonsCtrl.toggleEvent, function () {
        if (!element.hasClass(buttonsCtrl.activeClass)) {
          scope.$apply(function () {
            ngModelCtrl.$setViewValue(scope.$eval(attrs.btnRadio));
            ngModelCtrl.$render();
          });
        }
      });
    }
  };
})

.directive('btnCheckbox', function () {
  return {
    require: ['btnCheckbox', 'ngModel'],
    controller: 'ButtonsController',
    link: function (scope, element, attrs, ctrls) {
      var buttonsCtrl = ctrls[0], ngModelCtrl = ctrls[1];

      function getTrueValue() {
        return getCheckboxValue(attrs.btnCheckboxTrue, true);
      }

      function getFalseValue() {
        return getCheckboxValue(attrs.btnCheckboxFalse, false);
      }
      
      function getCheckboxValue(attributeValue, defaultValue) {
        var val = scope.$eval(attributeValue);
        return angular.isDefined(val) ? val : defaultValue;
      }

      //model -> UI
      ngModelCtrl.$render = function () {
        element.toggleClass(buttonsCtrl.activeClass, angular.equals(ngModelCtrl.$modelValue, getTrueValue()));
      };

      //ui->model
      element.bind(buttonsCtrl.toggleEvent, function () {
        scope.$apply(function () {
          ngModelCtrl.$setViewValue(element.hasClass(buttonsCtrl.activeClass) ? getFalseValue() : getTrueValue());
          ngModelCtrl.$render();
        });
      });
    }
  };
});

angular.module('mm.foundation.position', [])

/**
 * A set of utility methods that can be use to retrieve position of DOM elements.
 * It is meant to be used where we need to absolute-position DOM elements in
 * relation to other, existing elements (this is the case for tooltips, popovers,
 * typeahead suggestions etc.).
 */
  .factory('$position', ['$document', '$window', function ($document, $window) {

    function getStyle(el, cssprop) {
      if (el.currentStyle) { //IE
        return el.currentStyle[cssprop];
      } else if ($window.getComputedStyle) {
        return $window.getComputedStyle(el)[cssprop];
      }
      // finally try and get inline style
      return el.style[cssprop];
    }

    /**
     * Checks if a given element is statically positioned
     * @param element - raw DOM element
     */
    function isStaticPositioned(element) {
      return (getStyle(element, "position") || 'static' ) === 'static';
    }

    /**
     * returns the closest, non-statically positioned parentOffset of a given element
     * @param element
     */
    var parentOffsetEl = function (element) {
      var docDomEl = $document[0];
      var offsetParent = element.offsetParent || docDomEl;
      while (offsetParent && offsetParent !== docDomEl && isStaticPositioned(offsetParent) ) {
        offsetParent = offsetParent.offsetParent;
      }
      return offsetParent || docDomEl;
    };

    return {
      /**
       * Provides read-only equivalent of jQuery's position function:
       * http://api.jquery.com/position/
       */
      position: function (element) {
        var elBCR = this.offset(element);
        var offsetParentBCR = { top: 0, left: 0 };
        var offsetParentEl = parentOffsetEl(element[0]);
        if (offsetParentEl != $document[0]) {
          offsetParentBCR = this.offset(angular.element(offsetParentEl));
          offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
          offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
        }

        var boundingClientRect = element[0].getBoundingClientRect();
        return {
          width: boundingClientRect.width || element.prop('offsetWidth'),
          height: boundingClientRect.height || element.prop('offsetHeight'),
          top: elBCR.top - offsetParentBCR.top,
          left: elBCR.left - offsetParentBCR.left
        };
      },

      /**
       * Provides read-only equivalent of jQuery's offset function:
       * http://api.jquery.com/offset/
       */
      offset: function (element) {
        var boundingClientRect = element[0].getBoundingClientRect();
        return {
          width: boundingClientRect.width || element.prop('offsetWidth'),
          height: boundingClientRect.height || element.prop('offsetHeight'),
          top: boundingClientRect.top + ($window.pageYOffset || $document[0].body.scrollTop || $document[0].documentElement.scrollTop),
          left: boundingClientRect.left + ($window.pageXOffset || $document[0].body.scrollLeft  || $document[0].documentElement.scrollLeft)
        };
      }
    };
  }]);

angular.module("mm.foundation.mediaQueries", [])
    .factory('matchMedia', ['$document', '$window', function($document, $window) {
        // MatchMedia for IE <= 9
        return $window.matchMedia || (function matchMedia(doc, undefined){
            var bool,
                docElem = doc.documentElement,
                refNode = docElem.firstElementChild || docElem.firstChild,
                // fakeBody required for <FF4 when executed in <head>
                fakeBody = doc.createElement("body"),
                div = doc.createElement("div");

            div.id = "mq-test-1";
            div.style.cssText = "position:absolute;top:-100em";
            fakeBody.style.background = "none";
            fakeBody.appendChild(div);

            return function (q) {
                div.innerHTML = "&shy;<style media=\"" + q + "\"> #mq-test-1 { width: 42px; }</style>";
                docElem.insertBefore(fakeBody, refNode);
                bool = div.offsetWidth === 42;
                docElem.removeChild(fakeBody);
                return {
                    matches: bool,
                    media: q
                };
            };

        }($document[0]));
    }])
    .factory('mediaQueries', ['$document', 'matchMedia', function($document, matchMedia) {
        var head = angular.element($document[0].querySelector('head'));
        head.append('<meta class="foundation-mq-topbar" />');
        head.append('<meta class="foundation-mq-small" />');
        head.append('<meta class="foundation-mq-medium" />');
        head.append('<meta class="foundation-mq-large" />');

        var regex = /^[\/\\'"]+|(;\s?})+|[\/\\'"]+$/g;
        var queries = {
            topbar: getComputedStyle(head[0].querySelector('meta.foundation-mq-topbar')).fontFamily.replace(regex, ''),
            small : getComputedStyle(head[0].querySelector('meta.foundation-mq-small')).fontFamily.replace(regex, ''),
            medium : getComputedStyle(head[0].querySelector('meta.foundation-mq-medium')).fontFamily.replace(regex, ''),
            large : getComputedStyle(head[0].querySelector('meta.foundation-mq-large')).fontFamily.replace(regex, '')
        };

        return {
            topbarBreakpoint: function () {
                return !matchMedia(queries.topbar).matches;
            },
            small: function () {
                return matchMedia(queries.small).matches;
            },
            medium: function () {
                return matchMedia(queries.medium).matches;
            },
            large: function () {
                return matchMedia(queries.large).matches;
            }
        };
    }]);

/*
 * dropdownToggle - Provides dropdown menu functionality
 * @restrict class or attribute
 * @example:

   <a dropdown-toggle="#dropdown-menu">My Dropdown Menu</a>
   <ul id="dropdown-menu" class="f-dropdown">
     <li ng-repeat="choice in dropChoices">
       <a ng-href="{{choice.href}}">{{choice.text}}</a>
     </li>
   </ul>
 */
angular.module('mm.foundation.dropdownToggle', [ 'mm.foundation.position', 'mm.foundation.mediaQueries' ])

.controller('DropdownToggleController', ['$scope', '$attrs', 'mediaQueries', function($scope, $attrs, mediaQueries) {
  this.small = function() {
    return mediaQueries.small() && !mediaQueries.medium();
  };
}])

.directive('dropdownToggle', ['$document', '$window', '$location', '$position', function ($document, $window, $location, $position) {
  var openElement = null,
      closeMenu   = angular.noop;
  return {
    restrict: 'CA',
    controller: 'DropdownToggleController',
    link: function(scope, element, attrs, controller) {
      var parent = element.parent(),
          dropdown = angular.element($document[0].querySelector(attrs.dropdownToggle));

      var parentHasDropdown = function() {
        return parent.hasClass('has-dropdown');
      };

      var onClick = function (event) {
        dropdown = angular.element($document[0].querySelector(attrs.dropdownToggle));
        var elementWasOpen = (element === openElement);

        event.preventDefault();
        event.stopPropagation();

        if (!!openElement) {
          closeMenu();
        }

        if (!elementWasOpen && !element.hasClass('disabled') && !element.prop('disabled')) {
          dropdown.css('display', 'block'); // We display the element so that offsetParent is populated
          dropdown.addClass('f-open-dropdown');
          
          var offset = $position.offset(element);
          var parentOffset = $position.offset(angular.element(dropdown[0].offsetParent));
          var dropdownWidth = dropdown.prop('offsetWidth');
          var css = {
            top: offset.top - parentOffset.top + offset.height + 'px'
          };

          if (controller.small()) {
            css.left = Math.max((parentOffset.width - dropdownWidth) / 2, 8) + 'px';
            css.position = 'absolute';
            css.width = '95%';
            css['max-width'] = 'none';
          }
          else {
            var left = Math.round(offset.left - parentOffset.left);
            var rightThreshold = $window.innerWidth - dropdownWidth - 8;
            if (left > rightThreshold) {
                left = rightThreshold;
                dropdown.removeClass('left').addClass('right');
            }
            css.left = left + 'px';
            css.position = null;
            css['max-width'] = null;
          }

          dropdown.css(css);
          element.addClass('expanded');

          if (parentHasDropdown()) {
            parent.addClass('hover');
          }

          openElement = element;

          closeMenu = function (event) {
            $document.off('click', closeMenu);
            dropdown.css('display', 'none');
            dropdown.removeClass('f-open-dropdown');
            element.removeClass('expanded');
            closeMenu = angular.noop;
            openElement = null;
            if (parent.hasClass('hover')) {
              parent.removeClass('hover');
            }
          };
          $document.on('click', closeMenu);
        }
      };

      if (dropdown) {
        dropdown.css('display', 'none');
      }

      scope.$watch('$location.path', function() { closeMenu(); });

      element.on('click', onClick);
      element.on('$destroy', function() {
        element.off('click', onClick);
      });
    }
  };
}]);

/**
 * @ngdoc service
 * @name mm.foundation.interchange
 * @description
 *
 * Package containing all services and directives
 * about the `interchange` module
 */
angular.module('mm.foundation.interchange', ['mm.foundation.mediaQueries'])

  /**
   * @ngdoc function
   * @name mm.foundation.interchange.interchageQuery
   * @function interchageQuery
   * @description
   *
   * this service inject meta tags objects in the head
   * to get the list of media queries from Foundation
   * stylesheets.
   *
   * @return {object} Queries list name => mediaQuery
   */
  .factory('interchangeQueries', ['$document', function ($document) {
    var element,
      mediaSize,
      formatList = {
      'default': 'only screen',
      landscape : 'only screen and (orientation: landscape)',
      portrait : 'only screen and (orientation: portrait)',
      retina : 'only screen and (-webkit-min-device-pixel-ratio: 2),' +
        'only screen and (min--moz-device-pixel-ratio: 2),' +
        'only screen and (-o-min-device-pixel-ratio: 2/1),' +
        'only screen and (min-device-pixel-ratio: 2),' +
        'only screen and (min-resolution: 192dpi),' +
        'only screen and (min-resolution: 2dppx)'
    },
    classPrefix = 'foundation-mq-',
    classList = ['small', 'medium', 'large', 'xlarge', 'xxlarge'],
    head = angular.element($document[0].querySelector('head'));

    for (var i = 0; i < classList.length; i++) {
      head.append('<meta class="' + classPrefix + classList[i] + '" />');
      element = getComputedStyle(head[0].querySelector('meta.' + classPrefix + classList[i]));
      mediaSize = element.fontFamily.replace(/^[\/\\'"]+|(;\s?})+|[\/\\'"]+$/g, '');
      formatList[classList[i]] = mediaSize;
    }
    return formatList;
  }])

  /**
   * @ngdoc function
   * @name mm.foundation.interchange.interchangeQueriesManager
   * @function interchangeQueriesManager
   * @description
   *
   * interface to add and remove named queries
   * in the interchangeQueries list
   */
  .factory('interchangeQueriesManager', ['interchangeQueries', function (interchangeQueries) {
    return {
      /**
       * @ngdoc method
       * @name interchangeQueriesManager#add
       * @methodOf mm.foundation.interchange.interchangeQueriesManager
       * @description
       *
       * Add a custom media query in the `interchangeQueries`
       * factory. This method does not allow to update an existing
       * media query.
       *
       * @param {string} name MediaQuery name
       * @param {string} media MediaQuery
       * @returns {boolean} True if the insert is a success
       */
      add: function (name, media) {
        if (!name || !media ||
          !angular.isString(name) || !angular.isString(media) ||
          !!interchangeQueries[name]) {
          return false;
        }
        interchangeQueries[name] = media;
        return true;
      }
    };
  }])

  /**
   * @ngdoc function
   * @name mm.foundation.interchange.interchangeTools
   * @function interchangeTools
   * @description
   *
   * Tools to help with the `interchange` module.
   */
  .factory('interchangeTools', ['$window', 'matchMedia', 'interchangeQueries', function ($window, matchMedia, namedQueries) {

    /**
     * @ngdoc method
     * @name interchangeTools#parseAttribute
     * @methodOf mm.foundation.interchange.interchangeTools
     * @description
     *
     * Attribute parser to transform an `interchange` attribute
     * value to an object with media query (name or query) as key,
     * and file to use as value.
     *
     * ```
     * {
     *   small: 'bridge-500.jpg',
     *   large: 'bridge-1200.jpg'
     * }
     * ```
     *
     * @param {string} value Interchange query string
     * @returns {object} Attribute parsed
     */
    var parseAttribute = function (value) {
      var raw = value.split(/\[(.*?)\]/),
        i = raw.length,
        breaker = /^(.+)\,\ \((.+)\)$/,
        breaked,
        output = {};

      while (i--) {
        if (raw[i].replace(/[\W\d]+/, '').length > 4) {
          breaked = breaker.exec(raw[i]);
          if (!!breaked && breaked.length === 3) {
            output[breaked[2]] = breaked[1];
          }
        }
      }
      return output;
    };

    /**
     * @ngdoc method
     * @name interchangeTools#findCurrentMediaFile
     * @methodOf mm.foundation.interchange.interchangeTools
     * @description
     *
     * Find the current item to display from a file list
     * (object returned by `parseAttribute`) and the
     * current page dimensions.
     *
     * ```
     * {
     *   small: 'bridge-500.jpg',
     *   large: 'bridge-1200.jpg'
     * }
     * ```
     *
     * @param {object} files Parsed version of `interchange` attribute
     * @returns {string} File to display (or `undefined`)
     */
    var findCurrentMediaFile = function (files) {
      var file, media, match;
      for (file in files) {
        media = namedQueries[file] || file;
        match = matchMedia(media);
        if (match.matches) {
          return files[file];
        }
      }
      return;
    };

    return {
      parseAttribute: parseAttribute,
      findCurrentMediaFile: findCurrentMediaFile
    };
  }])

  /**
   * @ngdoc directive
   * @name mm.foundation.interchange.directive:interchange
   * @restrict A
   * @element DIV|IMG
   * @priority 450
   * @scope true
   * @description
   *
   * Interchange directive, following the same features as
   * ZURB documentation. The directive is splitted in 3 parts.
   *
   * 1. This directive use `compile` and not `link` for a simple
   * reason: if the method is applied on a DIV element to
   * display a template, the compile method will inject an ng-include.
   * Because using a `templateUrl` or `template` to do it wouldn't
   * be appropriate for all cases (`IMG` or dynamic backgrounds).
   * And doing it in `link` is too late to be applied.
   *
   * 2. In the `compile:post`, the attribute is parsed to find
   * out the type of content to display.
   *
   * 3. At the start and on event `resize`, the method `replace`
   * is called to reevaluate which file is supposed to be displayed
   * and update the value if necessary. The methd will also
   * trigger a `replace` event.
   */
  .directive('interchange', ['$window', '$rootScope', 'interchangeTools', function ($window, $rootScope, interchangeTools) {

    var pictureFilePattern = /[A-Za-z0-9-_]+\.(jpg|jpeg|png|gif|bmp|tiff)\ *,/i;

    return {
      restrict: 'A',
      scope: true,
      priority: 450,
      compile: function compile($element, attrs) {
        // Set up the attribute to update
        if ($element[0].nodeName === 'DIV' && !pictureFilePattern.test(attrs.interchange)) {
          $element.html('<ng-include src="currentFile"></ng-include>');
        }

        return {
          pre: function preLink($scope, $element, attrs) {},
          post: function postLink($scope, $element, attrs) {
            var currentFile, nodeName;

            // Set up the attribute to update
            nodeName = $element && $element[0] && $element[0].nodeName;
            $scope.fileMap = interchangeTools.parseAttribute(attrs.interchange);

            // Find the type of interchange
            switch (nodeName) {
            case 'DIV':
              // If the tag is a div, we test the current file to see if it's picture
              currentFile = interchangeTools.findCurrentMediaFile($scope.fileMap);
              if (/[A-Za-z0-9-_]+\.(jpg|jpeg|png|gif|bmp|tiff)$/i.test(currentFile)) {
                $scope.type = 'background';
              }
              else {
                $scope.type = 'include';
              }
              break;

            case 'IMG':
              $scope.type = 'image';
              break;

            default:
              return;
            }

            var replace = function (e) {
              // The the new file to display (exit if the same)
              var currentFile = interchangeTools.findCurrentMediaFile($scope.fileMap);
              if (!!$scope.currentFile && $scope.currentFile === currentFile) {
                return;
              }

              // Set up the new file
              $scope.currentFile = currentFile;
              switch ($scope.type) {
              case 'image':
                $element.attr('src', $scope.currentFile);
                break;

              case 'background':
                $element.css('background-image', 'url(' + $scope.currentFile + ')');
                break;
              }

              // Trigger events
              $rootScope.$emit('replace', $element, $scope);
              if (!!e) {
                $scope.$apply();
              }
            };

            // Start
            replace();
            $window.addEventListener('resize', replace);
            $scope.$on('$destroy', function () {
              $window.removeEventListener('resize', replace);
            });
          }
        };
      }
    };
  }]);

angular.module('mm.foundation.transition', [])

/**
 * $transition service provides a consistent interface to trigger CSS 3 transitions and to be informed when they complete.
 * @param  {DOMElement} element  The DOMElement that will be animated.
 * @param  {string|object|function} trigger  The thing that will cause the transition to start:
 *   - As a string, it represents the css class to be added to the element.
 *   - As an object, it represents a hash of style attributes to be applied to the element.
 *   - As a function, it represents a function to be called that will cause the transition to occur.
 * @return {Promise}  A promise that is resolved when the transition finishes.
 */
.factory('$transition', ['$q', '$timeout', '$rootScope', function($q, $timeout, $rootScope) {

  var $transition = function(element, trigger, options) {
    options = options || {};
    var deferred = $q.defer();
    var endEventName = $transition[options.animation ? "animationEndEventName" : "transitionEndEventName"];

    var transitionEndHandler = function(event) {
      $rootScope.$apply(function() {
        element.unbind(endEventName, transitionEndHandler);
        deferred.resolve(element);
      });
    };

    if (endEventName) {
      element.bind(endEventName, transitionEndHandler);
    }

    // Wrap in a timeout to allow the browser time to update the DOM before the transition is to occur
    $timeout(function() {
      if ( angular.isString(trigger) ) {
        element.addClass(trigger);
      } else if ( angular.isFunction(trigger) ) {
        trigger(element);
      } else if ( angular.isObject(trigger) ) {
        element.css(trigger);
      }
      //If browser does not support transitions, instantly resolve
      if ( !endEventName ) {
        deferred.resolve(element);
      }
    });

    // Add our custom cancel function to the promise that is returned
    // We can call this if we are about to run a new transition, which we know will prevent this transition from ending,
    // i.e. it will therefore never raise a transitionEnd event for that transition
    deferred.promise.cancel = function() {
      if ( endEventName ) {
        element.unbind(endEventName, transitionEndHandler);
      }
      deferred.reject('Transition cancelled');
    };

    return deferred.promise;
  };

  // Work out the name of the transitionEnd event
  var transElement = document.createElement('trans');
  var transitionEndEventNames = {
    'WebkitTransition': 'webkitTransitionEnd',
    'MozTransition': 'transitionend',
    'OTransition': 'oTransitionEnd',
    'transition': 'transitionend'
  };
  var animationEndEventNames = {
    'WebkitTransition': 'webkitAnimationEnd',
    'MozTransition': 'animationend',
    'OTransition': 'oAnimationEnd',
    'transition': 'animationend'
  };
  function findEndEventName(endEventNames) {
    for (var name in endEventNames){
      if (transElement.style[name] !== undefined) {
        return endEventNames[name];
      }
    }
  }
  $transition.transitionEndEventName = findEndEventName(transitionEndEventNames);
  $transition.animationEndEventName = findEndEventName(animationEndEventNames);
  return $transition;
}]);

angular.module('mm.foundation.modal', ['mm.foundation.transition'])

/**
 * A helper, internal data structure that acts as a map but also allows getting / removing
 * elements in the LIFO order
 */
  .factory('$$stackedMap', function () {
    return {
      createNew: function () {
        var stack = [];

        return {
          add: function (key, value) {
            stack.push({
              key: key,
              value: value
            });
          },
          get: function (key) {
            for (var i = 0; i < stack.length; i++) {
              if (key == stack[i].key) {
                return stack[i];
              }
            }
          },
          keys: function() {
            var keys = [];
            for (var i = 0; i < stack.length; i++) {
              keys.push(stack[i].key);
            }
            return keys;
          },
          top: function () {
            return stack[stack.length - 1];
          },
          remove: function (key) {
            var idx = -1;
            for (var i = 0; i < stack.length; i++) {
              if (key == stack[i].key) {
                idx = i;
                break;
              }
            }
            return stack.splice(idx, 1)[0];
          },
          removeTop: function () {
            return stack.splice(stack.length - 1, 1)[0];
          },
          length: function () {
            return stack.length;
          }
        };
      }
    };
  })

/**
 * A helper directive for the $modal service. It creates a backdrop element.
 */
  .directive('modalBackdrop', ['$modalStack', '$timeout', function ($modalStack, $timeout) {
    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'template/modal/backdrop.html',
      link: function (scope) {

        scope.animate = false;

        //trigger CSS transitions
        $timeout(function () {
          scope.animate = true;
        });

        scope.close = function (evt) {
          var modal = $modalStack.getTop();
          if (modal && modal.value.backdrop && modal.value.backdrop != 'static' && (evt.target === evt.currentTarget)) {
            evt.preventDefault();
            evt.stopPropagation();
            $modalStack.dismiss(modal.key, 'backdrop click');
          }
        };
      }
    };
  }])

  .directive('modalWindow', ['$modalStack', '$timeout', function ($modalStack, $timeout) {
    return {
      restrict: 'EA',
      scope: {
        index: '@',
        animate: '='
      },
      replace: true,
      transclude: true,
      templateUrl: 'template/modal/window.html',
      link: function (scope, element, attrs) {
        scope.windowClass = attrs.windowClass || '';

        $timeout(function () {
          // trigger CSS transitions
          scope.animate = true;

          // If the modal contains any autofocus elements refocus onto the first one
          if (element[0].querySelectorAll('[autofocus]').length > 0) {
            element[0].querySelectorAll('[autofocus]')[0].focus();
          }
          else{
          // otherwise focus the freshly-opened modal
            element[0].focus();
          }
        });
      }
    };
  }])

  .factory('$modalStack', ['$window', '$transition', '$timeout', '$document', '$compile', '$rootScope', '$$stackedMap',
    function ($window, $transition, $timeout, $document, $compile, $rootScope, $$stackedMap) {

      var OPENED_MODAL_CLASS = 'modal-open';

      var backdropDomEl, backdropScope, cssTop;
      var openedWindows = $$stackedMap.createNew();
      var $modalStack = {};

      function backdropIndex() {
        var topBackdropIndex = -1;
        var opened = openedWindows.keys();
        for (var i = 0; i < opened.length; i++) {
          if (openedWindows.get(opened[i]).value.backdrop) {
            topBackdropIndex = i;
          }
        }
        return topBackdropIndex;
      }

      $rootScope.$watch(backdropIndex, function(newBackdropIndex){
        if (backdropScope) {
          backdropScope.index = newBackdropIndex;
        }
      });

      function removeModalWindow(modalInstance) {
        var parent = $document.find(modalInstance.options.parent).eq(0);
        var modalWindow = openedWindows.get(modalInstance).value;

        //clean up the stack
        openedWindows.remove(modalInstance);

        //remove window DOM element
        removeAfterAnimate(modalWindow.modalDomEl, modalWindow.modalScope, 300, function() {
          modalWindow.modalScope.$destroy();
          parent.toggleClass(OPENED_MODAL_CLASS, openedWindows.length() > 0);
          checkRemoveBackdrop();
        });
      }

      function checkRemoveBackdrop() {
          //remove backdrop if no longer needed
          if (backdropDomEl && backdropIndex() == -1) {
            var backdropScopeRef = backdropScope;
            removeAfterAnimate(backdropDomEl, backdropScope, 150, function () {
              backdropScopeRef.$destroy();
              backdropScopeRef = null;
            });
            backdropDomEl = undefined;
            backdropScope = undefined;
          }
      }

      function removeAfterAnimate(domEl, scope, emulateTime, done) {
        // Closing animation
        scope.animate = false;

        var transitionEndEventName = $transition.transitionEndEventName;
        if (transitionEndEventName) {
          // transition out
          var timeout = $timeout(afterAnimating, emulateTime);

          domEl.bind(transitionEndEventName, function () {
            $timeout.cancel(timeout);
            afterAnimating();
            scope.$apply();
          });
        } else {
          // Ensure this call is async
          $timeout(afterAnimating, 0);
        }

        function afterAnimating() {
          if (afterAnimating.done) {
            return;
          }
          afterAnimating.done = true;

          domEl.remove();
          if (done) {
            done();
          }
        }
      }

      function calculateModalTop(modalElement, offset) {
        if (angular.isUndefined(offset)) {
          offset = 0;
        }
        var scrollY = $window.pageYOffset || 0;
        return offset + scrollY;
      }

      $document.bind('keydown', function (evt) {
        var modal;

        if (evt.which === 27) {
          modal = openedWindows.top();
          if (modal && modal.value.keyboard) {
            $rootScope.$apply(function () {
              $modalStack.dismiss(modal.key);
            });
          }
        }
      });

      $modalStack.open = function (modalInstance, modal) {
        modalInstance.options = {
          deferred: modal.deferred,
          modalScope: modal.scope,
          backdrop: modal.backdrop,
          keyboard: modal.keyboard,
          parent: modal.parent
        };
        openedWindows.add(modalInstance, modalInstance.options);

        var parent = $document.find(modal.parent).eq(0),
            currBackdropIndex = backdropIndex();

        if (currBackdropIndex >= 0 && !backdropDomEl) {
          backdropScope = $rootScope.$new(true);
          backdropScope.index = currBackdropIndex;
          backdropDomEl = $compile('<div modal-backdrop></div>')(backdropScope);
          parent.append(backdropDomEl);
        }

        // Create a faux modal div just to measure its
        // distance to top
        var faux = angular.element('<div class="reveal-modal" style="z-index:-1""></div>');
        parent.append(faux[0]);
        cssTop = parseInt($window.getComputedStyle(faux[0]).top) || 0;
        var openAt = calculateModalTop(faux, cssTop);
        faux.remove();

        var angularDomEl = angular.element('<div modal-window style="visibility: visible; top:' + openAt +'px;"></div>')
          .attr({
            'window-class': modal.windowClass,
            'index': openedWindows.length() - 1,
            'animate': 'animate'
          });
        angularDomEl.html(modal.content);

        var modalDomEl = $compile(angularDomEl)(modal.scope);
        openedWindows.top().value.modalDomEl = modalDomEl;
        parent.append(modalDomEl);
        parent.addClass(OPENED_MODAL_CLASS);
      };

      $modalStack.reposition = function (modalInstance) {
        var modalWindow = openedWindows.get(modalInstance).value;
        if (modalWindow) {
          var modalDomEl = modalWindow.modalDomEl;
          var top = calculateModalTop(modalDomEl, cssTop);
          modalDomEl.css('top', top + "px");
        }
      };

      $modalStack.close = function (modalInstance, result) {
        var modalWindow = openedWindows.get(modalInstance);
        if (modalWindow) {
          modalWindow.value.deferred.resolve(result);
          removeModalWindow(modalInstance);
        }
      };

      $modalStack.dismiss = function (modalInstance, reason) {
        var modalWindow = openedWindows.get(modalInstance);
        if (modalWindow) {
          modalWindow.value.deferred.reject(reason);
          removeModalWindow(modalInstance);
        }
      };

      $modalStack.dismissAll = function (reason) {
        var topModal = this.getTop();
        while (topModal) {
          this.dismiss(topModal.key, reason);
          topModal = this.getTop();
        }
      };

      $modalStack.getTop = function () {
        return openedWindows.top();
      };

      return $modalStack;
    }])

  .provider('$modal', function () {

    var $modalProvider = {
      options: {
        backdrop: true, //can be also false or 'static'
        keyboard: true
      },
      $get: ['$injector', '$rootScope', '$q', '$http', '$templateCache', '$controller', '$modalStack',
        function ($injector, $rootScope, $q, $http, $templateCache, $controller, $modalStack) {

          var $modal = {};

          function getTemplatePromise(options) {
            return options.template ? $q.when(options.template) :
              $http.get(options.templateUrl, {cache: $templateCache}).then(function (result) {
                return result.data;
              });
          }

          function getResolvePromises(resolves) {
            var promisesArr = [];
            angular.forEach(resolves, function (value, key) {
              if (angular.isFunction(value) || angular.isArray(value)) {
                promisesArr.push($q.when($injector.invoke(value)));
              }
            });
            return promisesArr;
          }

          $modal.open = function (modalOptions) {

            var modalResultDeferred = $q.defer();
            var modalOpenedDeferred = $q.defer();

            //prepare an instance of a modal to be injected into controllers and returned to a caller
            var modalInstance = {
              result: modalResultDeferred.promise,
              opened: modalOpenedDeferred.promise,
              close: function (result) {
                $modalStack.close(modalInstance, result);
              },
              dismiss: function (reason) {
                $modalStack.dismiss(modalInstance, reason);
              },
              reposition: function () {
                $modalStack.reposition(modalInstance);
              }
            };

            //merge and clean up options
            modalOptions = angular.extend({}, $modalProvider.options, modalOptions);
            modalOptions.resolve = modalOptions.resolve || {};

            //verify options
            if (!modalOptions.template && !modalOptions.templateUrl) {
              throw new Error('One of template or templateUrl options is required.');
            }

            var templateAndResolvePromise =
              $q.all([getTemplatePromise(modalOptions)].concat(getResolvePromises(modalOptions.resolve)));


            templateAndResolvePromise.then(function resolveSuccess(tplAndVars) {

              var modalScope = (modalOptions.scope || $rootScope).$new();
              modalScope.$close = modalInstance.close;
              modalScope.$dismiss = modalInstance.dismiss;

              var ctrlInstance, ctrlLocals = {};
              var resolveIter = 1;

              //controllers
              if (modalOptions.controller) {
                ctrlLocals.$scope = modalScope;
                ctrlLocals.$modalInstance = modalInstance;
                angular.forEach(modalOptions.resolve, function (value, key) {
                  ctrlLocals[key] = tplAndVars[resolveIter++];
                });

                ctrlInstance = $controller(modalOptions.controller, ctrlLocals);
                if (modalOptions.controllerAs) {
                  modalScope[modalOptions.controllerAs] = ctrlInstance;
                }
              }

              $modalStack.open(modalInstance, {
                scope: modalScope,
                deferred: modalResultDeferred,
                content: tplAndVars[0],
                backdrop: modalOptions.backdrop,
                keyboard: modalOptions.keyboard,
                windowClass: modalOptions.windowClass,
                parent: modalOptions.parent || 'body'
              });

            }, function resolveError(reason) {
              modalResultDeferred.reject(reason);
            });

            templateAndResolvePromise.then(function () {
              modalOpenedDeferred.resolve(true);
            }, function () {
              modalOpenedDeferred.reject(false);
            });

            return modalInstance;
          };

          return $modal;
        }]
    };

    return $modalProvider;
  });

angular.module("mm.foundation.offcanvas", [])
    .directive('offCanvasWrap', ['$window', function ($window) {
        return {
            scope: {},
            restrict: 'C',
            link: function ($scope, element, attrs) {
                var win = angular.element($window);
                var sidebar = $scope.sidebar = element;

                $scope.hide = function () {
                    sidebar.removeClass('move-left');
                    sidebar.removeClass('move-right');
                };

                win.bind("resize.body", $scope.hide);

                $scope.$on('$destroy', function() {
                    win.unbind("resize.body", $scope.hide);
                });

            },
            controller: ['$scope', function($scope) {

                this.leftToggle = function() {
                    $scope.sidebar.toggleClass("move-right");
                };

                this.rightToggle = function() {
                    $scope.sidebar.toggleClass("move-left");
                };

                this.hide = function() {
                    $scope.hide();
                };
            }]
        };
    }])
    .directive('leftOffCanvasToggle', [function () {
        return {
            require: '^offCanvasWrap',
            restrict: 'C',
            link: function ($scope, element, attrs, offCanvasWrap) {
                element.on('click', function () {
                    offCanvasWrap.leftToggle();
                });
            }
        };
    }])
    .directive('rightOffCanvasToggle', [function () {
        return {
            require: '^offCanvasWrap',
            restrict: 'C',
            link: function ($scope, element, attrs, offCanvasWrap) {
                element.on('click', function () {
                    offCanvasWrap.rightToggle();
                });
            }
        };
    }])
       .directive('exitOffCanvas', [function () {
        return {
            require: '^offCanvasWrap',
            restrict: 'C',
            link: function ($scope, element, attrs, offCanvasWrap) {
                element.on('click', function () {
                    offCanvasWrap.hide();
                });
            }
        };
    }])
    .directive('offCanvasList', [function () {
        return {
            require: '^offCanvasWrap',
            restrict: 'C',
            link: function ($scope, element, attrs, offCanvasWrap) {
                element.on('click', function () {
                    offCanvasWrap.hide();
                });
            }
        };
    }]);

angular.module('mm.foundation.pagination', [])

.controller('PaginationController', ['$scope', '$attrs', '$parse', '$interpolate', function ($scope, $attrs, $parse, $interpolate) {
  var self = this,
      setNumPages = $attrs.numPages ? $parse($attrs.numPages).assign : angular.noop;

  this.init = function(defaultItemsPerPage) {
    if ($attrs.itemsPerPage) {
      $scope.$parent.$watch($parse($attrs.itemsPerPage), function(value) {
        self.itemsPerPage = parseInt(value, 10);
        $scope.totalPages = self.calculateTotalPages();
      });
    } else {
      this.itemsPerPage = defaultItemsPerPage;
    }
  };

  this.noPrevious = function() {
    return this.page === 1;
  };
  this.noNext = function() {
    return this.page === $scope.totalPages;
  };

  this.isActive = function(page) {
    return this.page === page;
  };

  this.calculateTotalPages = function() {
    var totalPages = this.itemsPerPage < 1 ? 1 : Math.ceil($scope.totalItems / this.itemsPerPage);
    return Math.max(totalPages || 0, 1);
  };

  this.getAttributeValue = function(attribute, defaultValue, interpolate) {
    return angular.isDefined(attribute) ? (interpolate ? $interpolate(attribute)($scope.$parent) : $scope.$parent.$eval(attribute)) : defaultValue;
  };

  this.render = function() {
    this.page = parseInt($scope.page, 10) || 1;
    if (this.page > 0 && this.page <= $scope.totalPages) {
      $scope.pages = this.getPages(this.page, $scope.totalPages);
    }
  };

  $scope.selectPage = function(page) {
    if ( ! self.isActive(page) && page > 0 && page <= $scope.totalPages) {
      $scope.page = page;
      $scope.onSelectPage({ page: page });
    }
  };

  $scope.$watch('page', function() {
    self.render();
  });

  $scope.$watch('totalItems', function() {
    $scope.totalPages = self.calculateTotalPages();
  });

  $scope.$watch('totalPages', function(value) {
    setNumPages($scope.$parent, value); // Readonly variable

    if ( self.page > value ) {
      $scope.selectPage(value);
    } else {
      self.render();
    }
  });
}])

.constant('paginationConfig', {
  itemsPerPage: 10,
  boundaryLinks: false,
  directionLinks: true,
  firstText: 'First',
  previousText: 'Previous',
  nextText: 'Next',
  lastText: 'Last',
  rotate: true
})

.directive('pagination', ['$parse', 'paginationConfig', function($parse, config) {
  return {
    restrict: 'EA',
    scope: {
      page: '=',
      totalItems: '=',
      onSelectPage:' &'
    },
    controller: 'PaginationController',
    templateUrl: 'template/pagination/pagination.html',
    replace: true,
    link: function(scope, element, attrs, paginationCtrl) {

      // Setup configuration parameters
      var maxSize,
      boundaryLinks  = paginationCtrl.getAttributeValue(attrs.boundaryLinks,  config.boundaryLinks      ),
      directionLinks = paginationCtrl.getAttributeValue(attrs.directionLinks, config.directionLinks     ),
      firstText      = paginationCtrl.getAttributeValue(attrs.firstText,      config.firstText,     true),
      previousText   = paginationCtrl.getAttributeValue(attrs.previousText,   config.previousText,  true),
      nextText       = paginationCtrl.getAttributeValue(attrs.nextText,       config.nextText,      true),
      lastText       = paginationCtrl.getAttributeValue(attrs.lastText,       config.lastText,      true),
      rotate         = paginationCtrl.getAttributeValue(attrs.rotate,         config.rotate);

      paginationCtrl.init(config.itemsPerPage);

      if (attrs.maxSize) {
        scope.$parent.$watch($parse(attrs.maxSize), function(value) {
          maxSize = parseInt(value, 10);
          paginationCtrl.render();
        });
      }

      // Create page object used in template
      function makePage(number, text, isActive, isDisabled) {
        return {
          number: number,
          text: text,
          active: isActive,
          disabled: isDisabled
        };
      }

      paginationCtrl.getPages = function(currentPage, totalPages) {
        var pages = [];

        // Default page limits
        var startPage = 1, endPage = totalPages;
        var isMaxSized = ( angular.isDefined(maxSize) && maxSize < totalPages );

        // recompute if maxSize
        if ( isMaxSized ) {
          if ( rotate ) {
            // Current page is displayed in the middle of the visible ones
            startPage = Math.max(currentPage - Math.floor(maxSize/2), 1);
            endPage   = startPage + maxSize - 1;

            // Adjust if limit is exceeded
            if (endPage > totalPages) {
              endPage   = totalPages;
              startPage = endPage - maxSize + 1;
            }
          } else {
            // Visible pages are paginated with maxSize
            startPage = ((Math.ceil(currentPage / maxSize) - 1) * maxSize) + 1;

            // Adjust last page if limit is exceeded
            endPage = Math.min(startPage + maxSize - 1, totalPages);
          }
        }

        // Add page number links
        for (var number = startPage; number <= endPage; number++) {
          var page = makePage(number, number, paginationCtrl.isActive(number), false);
          pages.push(page);
        }

        // Add links to move between page sets
        if ( isMaxSized && ! rotate ) {
          if ( startPage > 1 ) {
            var previousPageSet = makePage(startPage - 1, '...', false, false);
            pages.unshift(previousPageSet);
          }

          if ( endPage < totalPages ) {
            var nextPageSet = makePage(endPage + 1, '...', false, false);
            pages.push(nextPageSet);
          }
        }

        // Add previous & next links
        if (directionLinks) {
          var previousPage = makePage(currentPage - 1, previousText, false, paginationCtrl.noPrevious());
          pages.unshift(previousPage);

          var nextPage = makePage(currentPage + 1, nextText, false, paginationCtrl.noNext());
          pages.push(nextPage);
        }

        // Add first & last links
        if (boundaryLinks) {
          var firstPage = makePage(1, firstText, false, paginationCtrl.noPrevious());
          pages.unshift(firstPage);

          var lastPage = makePage(totalPages, lastText, false, paginationCtrl.noNext());
          pages.push(lastPage);
        }

        return pages;
      };
    }
  };
}])

.constant('pagerConfig', {
  itemsPerPage: 10,
  previousText: '« Previous',
  nextText: 'Next »',
  align: true
})

.directive('pager', ['pagerConfig', function(config) {
  return {
    restrict: 'EA',
    scope: {
      page: '=',
      totalItems: '=',
      onSelectPage:' &'
    },
    controller: 'PaginationController',
    templateUrl: 'template/pagination/pager.html',
    replace: true,
    link: function(scope, element, attrs, paginationCtrl) {

      // Setup configuration parameters
      var previousText = paginationCtrl.getAttributeValue(attrs.previousText, config.previousText, true),
      nextText         = paginationCtrl.getAttributeValue(attrs.nextText,     config.nextText,     true),
      align            = paginationCtrl.getAttributeValue(attrs.align,        config.align);

      paginationCtrl.init(config.itemsPerPage);

      // Create page object used in template
      function makePage(number, text, isDisabled, isPrevious, isNext) {
        return {
          number: number,
          text: text,
          disabled: isDisabled,
          previous: ( align && isPrevious ),
          next: ( align && isNext )
        };
      }

      paginationCtrl.getPages = function(currentPage) {
        return [
          makePage(currentPage - 1, previousText, paginationCtrl.noPrevious(), true, false),
          makePage(currentPage + 1, nextText, paginationCtrl.noNext(), false, true)
        ];
      };
    }
  };
}]);

/**
 * The following features are still outstanding: animation as a
 * function, placement as a function, inside, support for more triggers than
 * just mouse enter/leave, html tooltips, and selector delegation.
 */
angular.module( 'mm.foundation.tooltip', [ 'mm.foundation.position', 'mm.foundation.bindHtml' ] )

/**
 * The $tooltip service creates tooltip- and popover-like directives as well as
 * houses global options for them.
 */
.provider( '$tooltip', function () {
  // The default options tooltip and popover.
  var defaultOptions = {
    placement: 'top',
    animation: true,
    popupDelay: 0
  };

  // Default hide triggers for each show trigger
  var triggerMap = {
    'mouseenter': 'mouseleave',
    'click': 'click',
    'focus': 'blur'
  };

  // The options specified to the provider globally.
  var globalOptions = {};
  
  /**
   * `options({})` allows global configuration of all tooltips in the
   * application.
   *
   *   var app = angular.module( 'App', ['mm.foundation.tooltip'], function( $tooltipProvider ) {
   *     // place tooltips left instead of top by default
   *     $tooltipProvider.options( { placement: 'left' } );
   *   });
   */
	this.options = function( value ) {
		angular.extend( globalOptions, value );
	};

  /**
   * This allows you to extend the set of trigger mappings available. E.g.:
   *
   *   $tooltipProvider.setTriggers( 'openTrigger': 'closeTrigger' );
   */
  this.setTriggers = function setTriggers ( triggers ) {
    angular.extend( triggerMap, triggers );
  };

  /**
   * This is a helper function for translating camel-case to snake-case.
   */
  function snake_case(name){
    var regexp = /[A-Z]/g;
    var separator = '-';
    return name.replace(regexp, function(letter, pos) {
      return (pos ? separator : '') + letter.toLowerCase();
    });
  }

  /**
   * Returns the actual instance of the $tooltip service.
   * TODO support multiple triggers
   */
  this.$get = [ '$window', '$compile', '$timeout', '$parse', '$document', '$position', '$interpolate', function ( $window, $compile, $timeout, $parse, $document, $position, $interpolate ) {
    return function $tooltip ( type, prefix, defaultTriggerShow ) {
      var options = angular.extend( {}, defaultOptions, globalOptions );

      /**
       * Returns an object of show and hide triggers.
       *
       * If a trigger is supplied,
       * it is used to show the tooltip; otherwise, it will use the `trigger`
       * option passed to the `$tooltipProvider.options` method; else it will
       * default to the trigger supplied to this directive factory.
       *
       * The hide trigger is based on the show trigger. If the `trigger` option
       * was passed to the `$tooltipProvider.options` method, it will use the
       * mapped trigger from `triggerMap` or the passed trigger if the map is
       * undefined; otherwise, it uses the `triggerMap` value of the show
       * trigger; else it will just use the show trigger.
       */
      function getTriggers ( trigger ) {
        var show = trigger || options.trigger || defaultTriggerShow;
        var hide = triggerMap[show] || show;
        return {
          show: show,
          hide: hide
        };
      }

      var directiveName = snake_case( type );

      var startSym = $interpolate.startSymbol();
      var endSym = $interpolate.endSymbol();
      var template = 
        '<div '+ directiveName +'-popup '+
          'title="'+startSym+'tt_title'+endSym+'" '+
          'content="'+startSym+'tt_content'+endSym+'" '+
          'placement="'+startSym+'tt_placement'+endSym+'" '+
          'animation="tt_animation" '+
          'is-open="tt_isOpen"'+
          '>'+
        '</div>';

      return {
        restrict: 'EA',
        scope: true,
        compile: function (tElem, tAttrs) {
          var tooltipLinker = $compile( template );

          return function link ( scope, element, attrs ) {
            var tooltip;
            var transitionTimeout;
            var popupTimeout;
            var appendToBody = angular.isDefined( options.appendToBody ) ? options.appendToBody : false;
            var triggers = getTriggers( undefined );
            var hasRegisteredTriggers = false;
            var hasEnableExp = angular.isDefined(attrs[prefix+'Enable']);

            var positionTooltip = function (){
              var position,
                ttWidth,
                ttHeight,
                ttPosition;
              // Get the position of the directive element.
              position = appendToBody ? $position.offset( element ) : $position.position( element );

              // Get the height and width of the tooltip so we can center it.
              ttWidth = tooltip.prop( 'offsetWidth' );
              ttHeight = tooltip.prop( 'offsetHeight' );

              // Calculate the tooltip's top and left coordinates to center it with
              // this directive.
              switch ( scope.tt_placement ) {
                case 'right':
                  ttPosition = {
                    top: position.top + position.height / 2 - ttHeight / 2,
                    left: position.left + position.width + 10
                  };
                  break;
                case 'bottom':
                  ttPosition = {
                    top: position.top + position.height + 10,
                    left: position.left
                  };
                  break;
                case 'left':
                  ttPosition = {
                    top: position.top + position.height / 2 - ttHeight / 2,
                    left: position.left - ttWidth - 10
                  };
                  break;
                default:
                  ttPosition = {
                    top: position.top - ttHeight - 10,
                    left: position.left
                  };
                  break;
              }

              ttPosition.top += 'px';
              ttPosition.left += 'px';

              // Now set the calculated positioning.
              tooltip.css( ttPosition );

            };

            // By default, the tooltip is not open.
            // TODO add ability to start tooltip opened
            scope.tt_isOpen = false;

            function toggleTooltipBind () {
              if ( ! scope.tt_isOpen ) {
                showTooltipBind();
              } else {
                hideTooltipBind();
              }
            }

            // Show the tooltip with delay if specified, otherwise show it immediately
            function showTooltipBind() {
              if(hasEnableExp && !scope.$eval(attrs[prefix+'Enable'])) {
                return;
              }
              if ( scope.tt_popupDelay ) {
                popupTimeout = $timeout( show, scope.tt_popupDelay, false );
                popupTimeout.then(function(reposition){reposition();});
              } else {
                show()();
              }
            }

            function hideTooltipBind () {
              scope.$apply(function () {
                hide();
              });
            }

            // Show the tooltip popup element.
            function show() {


              // Don't show empty tooltips.
              if ( ! scope.tt_content ) {
                return angular.noop;
              }

              createTooltip();

              // If there is a pending remove transition, we must cancel it, lest the
              // tooltip be mysteriously removed.
              if ( transitionTimeout ) {
                $timeout.cancel( transitionTimeout );
              }

              // Set the initial positioning.
              tooltip.css({ top: 0, left: 0, display: 'block' });

              // Now we add it to the DOM because need some info about it. But it's not 
              // visible yet anyway.
              if ( appendToBody ) {
                  $document.find( 'body' ).append( tooltip );
              } else {
                element.after( tooltip );
              }

              positionTooltip();

              // And show the tooltip.
              scope.tt_isOpen = true;
              scope.$digest(); // digest required as $apply is not called

              // Return positioning function as promise callback for correct
              // positioning after draw.
              return positionTooltip;
            }

            // Hide the tooltip popup element.
            function hide() {
              // First things first: we don't show it anymore.
              scope.tt_isOpen = false;

              //if tooltip is going to be shown after delay, we must cancel this
              $timeout.cancel( popupTimeout );

              // And now we remove it from the DOM. However, if we have animation, we 
              // need to wait for it to expire beforehand.
              // FIXME: this is a placeholder for a port of the transitions library.
              if ( scope.tt_animation ) {
                transitionTimeout = $timeout(removeTooltip, 500);
              } else {
                removeTooltip();
              }
            }

            function createTooltip() {
              // There can only be one tooltip element per directive shown at once.
              if (tooltip) {
                removeTooltip();
              }
              tooltip = tooltipLinker(scope, function () {});

              // Get contents rendered into the tooltip
              scope.$digest();
            }

            function removeTooltip() {
              if (tooltip) {
                tooltip.remove();
                tooltip = null;
              }
            }

            /**
             * Observe the relevant attributes.
             */
            attrs.$observe( type, function ( val ) {
              scope.tt_content = val;

              if (!val && scope.tt_isOpen ) {
                hide();
              }
            });

            attrs.$observe( prefix+'Title', function ( val ) {
              scope.tt_title = val;
            });

            attrs[prefix+'Placement'] = attrs[prefix+'Placement'] || null;

            attrs.$observe( prefix+'Placement', function ( val ) {
              scope.tt_placement = angular.isDefined( val ) && val ? val : options.placement;
            });

            attrs[prefix+'PopupDelay'] = attrs[prefix+'PopupDelay'] || null;

            attrs.$observe( prefix+'PopupDelay', function ( val ) {
              var delay = parseInt( val, 10 );
              scope.tt_popupDelay = ! isNaN(delay) ? delay : options.popupDelay;
            });

            var unregisterTriggers = function() {
              if ( hasRegisteredTriggers ) {
                if ( angular.isFunction( triggers.show ) ) {
                  unregisterTriggerFunction();
                } else {
                  element.unbind( triggers.show, showTooltipBind );
                  element.unbind( triggers.hide, hideTooltipBind );
                }
              }
            };

            var unregisterTriggerFunction = function () {};

            attrs[prefix+'Trigger'] = attrs[prefix+'Trigger'] || null;

            attrs.$observe( prefix+'Trigger', function ( val ) {
              unregisterTriggers();
              unregisterTriggerFunction();

              triggers = getTriggers( val );

              if ( angular.isFunction( triggers.show ) ) {
                unregisterTriggerFunction = scope.$watch( function () {
                  return triggers.show( scope, element, attrs );
                }, function ( val ) {
                  return val ? $timeout( show ) : $timeout( hide );
                });
              } else {
                if ( triggers.show === triggers.hide ) {
                  element.bind( triggers.show, toggleTooltipBind );
                } else {
                  element.bind( triggers.show, showTooltipBind );
                  element.bind( triggers.hide, hideTooltipBind );
                }
              }

              hasRegisteredTriggers = true;
            });

            var animation = scope.$eval(attrs[prefix + 'Animation']);
            scope.tt_animation = angular.isDefined(animation) ? !!animation : options.animation;

            attrs.$observe( prefix+'AppendToBody', function ( val ) {
              appendToBody = angular.isDefined( val ) ? $parse( val )( scope ) : appendToBody;
            });

            // if a tooltip is attached to <body> we need to remove it on
            // location change as its parent scope will probably not be destroyed
            // by the change.
            if ( appendToBody ) {
              scope.$on('$locationChangeSuccess', function closeTooltipOnLocationChangeSuccess () {
              if ( scope.tt_isOpen ) {
                hide();
              }
            });
            }

            // Make sure tooltip is destroyed and removed.
            scope.$on('$destroy', function onDestroyTooltip() {
              $timeout.cancel( transitionTimeout );
              $timeout.cancel( popupTimeout );
              unregisterTriggers();
              unregisterTriggerFunction();
              removeTooltip();
            });
          };
        }
      };
    };
  }];
})

.directive( 'tooltipPopup', function () {
  return {
    restrict: 'EA',
    replace: true,
    scope: { content: '@', placement: '@', animation: '&', isOpen: '&' },
    templateUrl: 'template/tooltip/tooltip-popup.html'
  };
})

.directive( 'tooltip', [ '$tooltip', function ( $tooltip ) {
  return $tooltip( 'tooltip', 'tooltip', 'mouseenter' );
}])

.directive( 'tooltipHtmlUnsafePopup', function () {
  return {
    restrict: 'EA',
    replace: true,
    scope: { content: '@', placement: '@', animation: '&', isOpen: '&' },
    templateUrl: 'template/tooltip/tooltip-html-unsafe-popup.html'
  };
})

.directive( 'tooltipHtmlUnsafe', [ '$tooltip', function ( $tooltip ) {
  return $tooltip( 'tooltipHtmlUnsafe', 'tooltip', 'mouseenter' );
}]);

/**
 * The following features are still outstanding: popup delay, animation as a
 * function, placement as a function, inside, support for more triggers than
 * just mouse enter/leave, html popovers, and selector delegatation.
 */
angular.module( 'mm.foundation.popover', [ 'mm.foundation.tooltip' ] )

.directive( 'popoverPopup', function () {
  return {
    restrict: 'EA',
    replace: true,
    scope: { title: '@', content: '@', placement: '@', animation: '&', isOpen: '&' },
    templateUrl: 'template/popover/popover.html'
  };
})

.directive( 'popover', [ '$tooltip', function ( $tooltip ) {
  return $tooltip( 'popover', 'popover', 'click' );
}]);

angular.module('mm.foundation.progressbar', ['mm.foundation.transition'])

.constant('progressConfig', {
  animate: true,
  max: 100
})

.controller('ProgressController', ['$scope', '$attrs', 'progressConfig', '$transition', function($scope, $attrs, progressConfig, $transition) {
    var self = this,
        bars = [],
        max = angular.isDefined($attrs.max) ? $scope.$parent.$eval($attrs.max) : progressConfig.max,
        animate = angular.isDefined($attrs.animate) ? $scope.$parent.$eval($attrs.animate) : progressConfig.animate;

    this.addBar = function(bar, element) {
        var oldValue = 0, index = bar.$parent.$index;
        if ( angular.isDefined(index) &&  bars[index] ) {
            oldValue = bars[index].value;
        }
        bars.push(bar);

        this.update(element, bar.value, oldValue);

        bar.$watch('value', function(value, oldValue) {
            if (value !== oldValue) {
                self.update(element, value, oldValue);
            }
        });

        bar.$on('$destroy', function() {
            self.removeBar(bar);
        });
    };

    // Update bar element width
    this.update = function(element, newValue, oldValue) {
        var percent = this.getPercentage(newValue);

        if (animate) {
            element.css('width', this.getPercentage(oldValue) + '%');
            $transition(element, {width: percent + '%'});
        } else {
            element.css({'transition': 'none', 'width': percent + '%'});
        }
    };

    this.removeBar = function(bar) {
        bars.splice(bars.indexOf(bar), 1);
    };

    this.getPercentage = function(value) {
        return Math.round(100 * value / max);
    };
}])

.directive('progress', function() {
    return {
        restrict: 'EA',
        replace: true,
        transclude: true,
        controller: 'ProgressController',
        require: 'progress',
        scope: {},
        template: '<div class="progress" ng-transclude></div>'
        //templateUrl: 'template/progressbar/progress.html' // Works in AngularJS 1.2
    };
})

.directive('bar', function() {
    return {
        restrict: 'EA',
        replace: true,
        transclude: true,
        require: '^progress',
        scope: {
            value: '=',
            type: '@'
        },
        templateUrl: 'template/progressbar/bar.html',
        link: function(scope, element, attrs, progressCtrl) {
            progressCtrl.addBar(scope, element);
        }
    };
})

.directive('progressbar', function() {
    return {
        restrict: 'EA',
        replace: true,
        transclude: true,
        controller: 'ProgressController',
        scope: {
            value: '=',
            type: '@'
        },
        templateUrl: 'template/progressbar/progressbar.html',
        link: function(scope, element, attrs, progressCtrl) {
            progressCtrl.addBar(scope, angular.element(element.children()[0]));
        }
    };
});

angular.module('mm.foundation.rating', [])

.constant('ratingConfig', {
  max: 5,
  stateOn: null,
  stateOff: null
})

.controller('RatingController', ['$scope', '$attrs', '$parse', 'ratingConfig', function($scope, $attrs, $parse, ratingConfig) {

  this.maxRange = angular.isDefined($attrs.max) ? $scope.$parent.$eval($attrs.max) : ratingConfig.max;
  this.stateOn = angular.isDefined($attrs.stateOn) ? $scope.$parent.$eval($attrs.stateOn) : ratingConfig.stateOn;
  this.stateOff = angular.isDefined($attrs.stateOff) ? $scope.$parent.$eval($attrs.stateOff) : ratingConfig.stateOff;

  this.createRateObjects = function(states) {
    var defaultOptions = {
      stateOn: this.stateOn,
      stateOff: this.stateOff
    };

    for (var i = 0, n = states.length; i < n; i++) {
      states[i] = angular.extend({ index: i }, defaultOptions, states[i]);
    }
    return states;
  };

  // Get objects used in template
  $scope.range = angular.isDefined($attrs.ratingStates) ?  this.createRateObjects(angular.copy($scope.$parent.$eval($attrs.ratingStates))): this.createRateObjects(new Array(this.maxRange));

  $scope.rate = function(value) {
    if ( $scope.value !== value && !$scope.readonly ) {
      $scope.value = value;
    }
  };

  $scope.enter = function(value) {
    if ( ! $scope.readonly ) {
      $scope.val = value;
    }
    $scope.onHover({value: value});
  };

  $scope.reset = function() {
    $scope.val = angular.copy($scope.value);
    $scope.onLeave();
  };

  $scope.$watch('value', function(value) {
    $scope.val = value;
  });

  $scope.readonly = false;
  if ($attrs.readonly) {
    $scope.$parent.$watch($parse($attrs.readonly), function(value) {
      $scope.readonly = !!value;
    });
  }
}])

.directive('rating', function() {
  return {
    restrict: 'EA',
    scope: {
      value: '=',
      onHover: '&',
      onLeave: '&'
    },
    controller: 'RatingController',
    templateUrl: 'template/rating/rating.html',
    replace: true
  };
});


/**
 * @ngdoc overview
 * @name mm.foundation.tabs
 *
 * @description
 * AngularJS version of the tabs directive.
 */

angular.module('mm.foundation.tabs', [])

.controller('TabsetController', ['$scope', function TabsetCtrl($scope) {
  var ctrl = this,
      tabs = ctrl.tabs = $scope.tabs = [];

  if (angular.isUndefined($scope.openOnLoad)) { $scope.openOnLoad = true; }

  ctrl.select = function(tab) {
    angular.forEach(tabs, function(tab) {
      tab.active = false;
    });
    tab.active = true;
  };

  ctrl.addTab = function addTab(tab) {
    tabs.push(tab);
    if ($scope.openOnLoad && (tabs.length === 1 || tab.active)) {
      ctrl.select(tab);
    }
  };

  ctrl.removeTab = function removeTab(tab) {
    var index = tabs.indexOf(tab);
    //Select a new tab if the tab to be removed is selected
    if (tab.active && tabs.length > 1) {
      //If this is the last tab, select the previous tab. else, the next tab.
      var newActiveIndex = index == tabs.length - 1 ? index - 1 : index + 1;
      ctrl.select(tabs[newActiveIndex]);
    }
    tabs.splice(index, 1);
  };
}])

/**
 * @ngdoc directive
 * @name mm.foundation.tabs.directive:tabset
 * @restrict EA
 *
 * @description
 * Tabset is the outer container for the tabs directive
 *
 * @param {boolean=} vertical Whether or not to use vertical styling for the tabs.
 * @param {boolean=} justified Whether or not to use justified styling for the tabs.
 *
 * @example
<example module="mm.foundation">
  <file name="index.html">
    <tabset>
      <tab heading="Tab 1"><b>First</b> Content!</tab>
      <tab heading="Tab 2"><i>Second</i> Content!</tab>
    </tabset>
    <hr />
    <tabset vertical="true">
      <tab heading="Vertical Tab 1"><b>First</b> Vertical Content!</tab>
      <tab heading="Vertical Tab 2"><i>Second</i> Vertical Content!</tab>
    </tabset>
    <tabset justified="true">
      <tab heading="Justified Tab 1"><b>First</b> Justified Content!</tab>
      <tab heading="Justified Tab 2"><i>Second</i> Justified Content!</tab>
    </tabset>
  </file>
</example>
 */
.directive('tabset', function() {
  return {
    restrict: 'EA',
    transclude: true,
    replace: true,
    scope: {
      openOnLoad: '=?'
    },
    controller: 'TabsetController',
    templateUrl: 'template/tabs/tabset.html',
    link: function(scope, element, attrs) {
      scope.vertical = angular.isDefined(attrs.vertical) ? scope.$parent.$eval(attrs.vertical) : false;
      scope.justified = angular.isDefined(attrs.justified) ? scope.$parent.$eval(attrs.justified) : false;
      scope.type = angular.isDefined(attrs.type) ? scope.$parent.$eval(attrs.type) : 'tabs';
    }
  };
})

/**
 * @ngdoc directive
 * @name mm.foundation.tabs.directive:tab
 * @restrict EA
 *
 * @param {string=} heading The visible heading, or title, of the tab. Set HTML headings with {@link mm.foundation.tabs.directive:tabHeading tabHeading}.
 * @param {string=} select An expression to evaluate when the tab is selected.
 * @param {boolean=} active A binding, telling whether or not this tab is selected.
 * @param {boolean=} disabled A binding, telling whether or not this tab is disabled.
 *
 * @description
 * Creates a tab with a heading and content. Must be placed within a {@link mm.foundation.tabs.directive:tabset tabset}.
 *
 * @example
<example module="mm.foundation">
  <file name="index.html">
    <div ng-controller="TabsDemoCtrl">
      <button class="button small" ng-click="items[0].active = true">
        Select item 1, using active binding
      </button>
      <button class="button small" ng-click="items[1].disabled = !items[1].disabled">
        Enable/disable item 2, using disabled binding
      </button>
      <br />
      <tabset>
        <tab heading="Tab 1">First Tab</tab>
        <tab select="alertMe()">
          <tab-heading><i class="fa fa-bell"></i> Alert me!</tab-heading>
          Second Tab, with alert callback and html heading!
        </tab>
        <tab ng-repeat="item in items"
          heading="{{item.title}}"
          disabled="item.disabled"
          active="item.active">
          {{item.content}}
        </tab>
      </tabset>
    </div>
  </file>
  <file name="script.js">
    function TabsDemoCtrl($scope) {
      $scope.items = [
        { title:"Dynamic Title 1", content:"Dynamic Item 0" },
        { title:"Dynamic Title 2", content:"Dynamic Item 1", disabled: true }
      ];

      $scope.alertMe = function() {
        setTimeout(function() {
          alert("You've selected the alert tab!");
        });
      };
    };
  </file>
</example>
 */

/**
 * @ngdoc directive
 * @name mm.foundation.tabs.directive:tabHeading
 * @restrict EA
 *
 * @description
 * Creates an HTML heading for a {@link mm.foundation.tabs.directive:tab tab}. Must be placed as a child of a tab element.
 *
 * @example
<example module="mm.foundation">
  <file name="index.html">
    <tabset>
      <tab>
        <tab-heading><b>HTML</b> in my titles?!</tab-heading>
        And some content, too!
      </tab>
      <tab>
        <tab-heading><i class="fa fa-heart"></i> Icon heading?!?</tab-heading>
        That's right.
      </tab>
    </tabset>
  </file>
</example>
 */
.directive('tab', ['$parse', function($parse) {
  return {
    require: '^tabset',
    restrict: 'EA',
    replace: true,
    templateUrl: 'template/tabs/tab.html',
    transclude: true,
    scope: {
      heading: '@',
      onSelect: '&select', //This callback is called in contentHeadingTransclude
                          //once it inserts the tab's content into the dom
      onDeselect: '&deselect'
    },
    controller: function() {
      //Empty controller so other directives can require being 'under' a tab
    },
    compile: function(elm, attrs, transclude) {
      return function postLink(scope, elm, attrs, tabsetCtrl) {
        var getActive, setActive;
        if (attrs.active) {
          getActive = $parse(attrs.active);
          setActive = getActive.assign;
          scope.$parent.$watch(getActive, function updateActive(value, oldVal) {
            // Avoid re-initializing scope.active as it is already initialized
            // below. (watcher is called async during init with value ===
            // oldVal)
            if (value !== oldVal) {
              scope.active = !!value;
            }
          });
          scope.active = getActive(scope.$parent);
        } else {
          setActive = getActive = angular.noop;
        }

        scope.$watch('active', function(active) {
          if( !angular.isFunction(setActive) ){
            return;
          }
          // Note this watcher also initializes and assigns scope.active to the
          // attrs.active expression.          
          setActive(scope.$parent, active);
          if (active) {
            tabsetCtrl.select(scope);
            scope.onSelect();
          } else {
            scope.onDeselect();
          }
        });

        scope.disabled = false;
        if ( attrs.disabled ) {
          scope.$parent.$watch($parse(attrs.disabled), function(value) {
            scope.disabled = !! value;
          });
        }

        scope.select = function() {
          if ( ! scope.disabled ) {
            scope.active = true;
          }
        };

        tabsetCtrl.addTab(scope);
        scope.$on('$destroy', function() {
          tabsetCtrl.removeTab(scope);
        });


        //We need to transclude later, once the content container is ready.
        //when this link happens, we're inside a tab heading.
        scope.$transcludeFn = transclude;
      };
    }
  };
}])

.directive('tabHeadingTransclude', [function() {
  return {
    restrict: 'A',
    require: '^tab',
    link: function(scope, elm, attrs, tabCtrl) {
      scope.$watch('headingElement', function updateHeadingElement(heading) {
        if (heading) {
          elm.html('');
          elm.append(heading);
        }
      });
    }
  };
}])

.directive('tabContentTransclude', function() {
  return {
    restrict: 'A',
    require: '^tabset',
    link: function(scope, elm, attrs) {
      var tab = scope.$eval(attrs.tabContentTransclude);

      //Now our tab is ready to be transcluded: both the tab heading area
      //and the tab content area are loaded.  Transclude 'em both.
      tab.$transcludeFn(tab.$parent, function(contents) {
        angular.forEach(contents, function(node) {
          if (isTabHeading(node)) {
            //Let tabHeadingTransclude know.
            tab.headingElement = node;
          } else {
            elm.append(node);
          }
        });
      });
    }
  };
  function isTabHeading(node) {
    return node.tagName &&  (
      node.hasAttribute('tab-heading') ||
      node.hasAttribute('data-tab-heading') ||
      node.tagName.toLowerCase() === 'tab-heading' ||
      node.tagName.toLowerCase() === 'data-tab-heading'
    );
  }
})

;

angular.module("mm.foundation.topbar", ['mm.foundation.mediaQueries'])
.factory('closest', [function() {
  return function(el, selector) {
    var matchesSelector = function (node, selector) {
      var nodes = (node.parentNode || node.document).querySelectorAll(selector);
      var i = -1;
      while (nodes[++i] && nodes[i] != node){}
      return !!nodes[i];
    };

    var element = el[0];
    while (element) {
      if (matchesSelector(element, selector)) {
        return angular.element(element);
      } else {
        element = element.parentElement;
      }
    }
    return false;
  };
}])
.directive('topBar', ['$timeout','$compile', '$window', '$document', 'mediaQueries',
  function($timeout, $compile, $window, $document, mediaQueries) {
    return {
      scope: {
        stickyClass : '@',
        backText: '@',
        stickyOn : '=',
        customBackText: '=',
        isHover: '=',
        mobileShowParentLink: '=',
        scrolltop : '=',
      },
      restrict: 'EA',
      replace: true,
      templateUrl: 'template/topbar/top-bar.html',
      transclude: true,
      controller: ['$window', '$scope', 'closest', function($window, $scope, closest) {
        $scope.settings = {};
        $scope.settings.stickyClass = $scope.stickyClass || 'sticky';
        $scope.settings.backText = $scope.backText || 'Back';
        $scope.settings.stickyOn = $scope.stickyOn || 'all';

        $scope.settings.customBackText = $scope.customBackText === undefined ? true : $scope.customBackText;
        $scope.settings.isHover = $scope.isHover === undefined ? true : $scope.isHover;
        $scope.settings.mobileShowParentLink = $scope.mobileShowParentLink === undefined ? true : $scope.mobileShowParentLink;
        $scope.settings.scrolltop = $scope.scrolltop === undefined ? true : $scope.scrolltop; // jump to top when sticky nav menu toggle is clicked

        this.settings = $scope.settings;

        $scope.index = 0;

        var outerHeight = function(el) {
          var height = el.offsetHeight;
          var style = el.currentStyle || getComputedStyle(el);

          height += parseInt(style.marginTop, 10) + parseInt(style.marginBottom, 10);
          return height;
        };


        var sections = [];

        this.addSection = function(section) {
          sections.push(section);
        };

        this.removeSection = function(section) {
          var index = sections.indexOf(section);
          if (index > -1) {
            sections.splice(index, 1);
          }
        };

        var dir = /rtl/i.test($document.find('html').attr('dir')) ? 'right' : 'left';

        $scope.$watch('index', function(index) {
          for(var i = 0; i < sections.length; i++){
            sections[i].move(dir, index);
          }
        });

        this.toggle = function(on) {
          $scope.toggle(on);
          for(var i = 0; i < sections.length; i++){
            sections[i].reset();
          }
          $scope.index = 0;
          $scope.height = '';
          $scope.$apply();
        };

        this.back = function(event) {
          if($scope.index < 1 || !mediaQueries.topbarBreakpoint()){
            return;
          }

          var $link = angular.element(event.currentTarget);
          var $movedLi = closest($link, 'li.moved');
          var $previousLevelUl = $movedLi.parent();
          $scope.index = $scope.index -1;

          if($scope.index === 0){
            $scope.height = '';
          } else {
            $scope.height = $scope.originalHeight + outerHeight($previousLevelUl[0]);
          }

          $timeout(function() {
            $movedLi.removeClass('moved');
          }, 300);
        };

        this.forward = function(event) {
          if(!mediaQueries.topbarBreakpoint()){
            return false;
          }

          var $link = angular.element(event.currentTarget);
          var $selectedLi = closest($link, 'li');
          $selectedLi.addClass('moved');
          $scope.height = $scope.originalHeight + outerHeight($link.parent()[0].querySelector('ul'));
          $scope.index = $scope.index + 1;
          $scope.$apply();
        };

      }],
      link: function(scope, element, attrs) {
        var topbar = scope.topbar = element;
        var topbarContainer = topbar.parent();
        var body = angular.element($document[0].querySelector('body'));
        var lastBreakpoint = mediaQueries.topbarBreakpoint();

        var isSticky = scope.isSticky = function() {
          var sticky = topbarContainer.hasClass(scope.settings.stickyClass);
          if (sticky && scope.settings.stickyOn === 'all') {
            return true;
          } else if (sticky && mediaQueries.small() && scope.settings.stickyOn === 'small') {
            return true;
          } else if (sticky && mediaQueries.medium() && scope.settings.stickyOn === 'medium') {
            return true;
          } else if (sticky && mediaQueries.large() && scope.settings.stickyOn === 'large') {
            return true;
          }
          return false;
        };

        var updateStickyPositioning = function() {
          if (!scope.stickyTopbar || !scope.isSticky()) {
            return;
          }

          var distance = stickyoffset;

          if ($window.pageYOffset > distance && !topbarContainer.hasClass('fixed')) {
            topbarContainer.addClass('fixed');
            body.css('padding-top', scope.originalHeight + 'px');
          } else if ($window.pageYOffset <= distance && topbarContainer.hasClass('fixed')) {
            topbarContainer.removeClass('fixed');
            body.css('padding-top', '');
          }
        };

        var onResize = function() {
          var currentBreakpoint = mediaQueries.topbarBreakpoint();
          if(lastBreakpoint === currentBreakpoint){
            return;
          }
          lastBreakpoint = mediaQueries.topbarBreakpoint();

          topbar.removeClass('expanded');
          topbar.parent().removeClass('expanded');
          scope.height = '';

          var sections = angular.element(topbar[0].querySelectorAll('section'));
          angular.forEach(sections, function(section) {
            angular.element(section.querySelectorAll('li.moved')).removeClass('moved');
          });

          scope.$apply();
        };

        var onScroll = function() {
          updateStickyPositioning();
          scope.$apply();
        };

        scope.toggle = function(on) {
          if(!mediaQueries.topbarBreakpoint()){
            return false;
          }

          var expand = (on === undefined) ? !topbar.hasClass('expanded') : on;

          if (expand) {
            topbar.addClass('expanded');
          }
          else {
            topbar.removeClass('expanded');
          }

          if (scope.settings.scrolltop) {
            if (!expand && topbar.hasClass('fixed')) {
              topbar.parent().addClass('fixed');
              topbar.removeClass('fixed');
              body.css('padding-top', scope.originalHeight + 'px');
            } else if (expand && topbar.parent().hasClass('fixed')) {
              topbar.parent().removeClass('fixed');
              topbar.addClass('fixed');
              body.css('padding-top', '');
              $window.scrollTo(0,0);
            }
          } else {
            if(isSticky()) {
              topbar.parent().addClass('fixed');
            }

            if(topbar.parent().hasClass('fixed')) {
              if (!expand) {
                topbar.removeClass('fixed');
                topbar.parent().removeClass('expanded');
                updateStickyPositioning();
              } else {
                topbar.addClass('fixed');
                topbar.parent().addClass('expanded');
                body.css('padding-top', scope.originalHeight + 'px');
              }
            }
          }
        };

        if(topbarContainer.hasClass('fixed') || isSticky() ) {
          scope.stickyTopbar = true;
          scope.height = topbarContainer[0].offsetHeight;
          var stickyoffset = topbarContainer[0].getBoundingClientRect().top + $window.pageYOffset;
        } else {
          scope.height = topbar[0].offsetHeight;
        }

        scope.originalHeight = scope.height;

        scope.$watch('height', function(h) {
          if(h){
            topbar.css('height', h + 'px');
          } else {
            topbar.css('height', '');
          }
        });

        angular.element($window).bind('resize', onResize);
        angular.element($window).bind('scroll', onScroll);

        scope.$on('$destroy', function() {
          angular.element($window).unbind('scroll', onResize);
          angular.element($window).unbind('resize', onScroll);
        });

        if (topbarContainer.hasClass('fixed')) {
          body.css('padding-top', scope.originalHeight + 'px');
        }
      }
    };
  }]
)
.directive('toggleTopBar', ['closest', function (closest) {
  return {
    scope: {},
    require: '^topBar',
    restrict: 'A',
    replace: true,
    templateUrl: 'template/topbar/toggle-top-bar.html',
    transclude: true,
    link: function(scope, element, attrs, topBar) {
      element.bind('click', function(event) {
        var li = closest(angular.element(event.currentTarget), 'li');
        if(!li.hasClass('back') && !li.hasClass('has-dropdown')) {
          topBar.toggle();
        }
      });

      scope.$on('$destroy', function() {
        element.unbind('click');
      });
    }
  };
}])
.directive('topBarSection', ['$compile', 'closest', function($compile, closest) {
  return {
    scope: {},
    require: '^topBar',
    restrict: 'EA',
    replace: true,
    templateUrl: 'template/topbar/top-bar-section.html',
    transclude: true,
    link: function(scope, element, attrs, topBar) {
      var section = element;

      scope.reset = function() {
        angular.element(section[0].querySelectorAll('li.moved')).removeClass('moved');
      };

      scope.move = function(dir, index) {
        if(dir === 'left'){
          section.css({"left": index * -100 + '%'});
        }
        else {
          section.css({"right": index * -100 + '%'});
        }
      };

      topBar.addSection(scope);

      scope.$on("$destroy", function() {
        topBar.removeSection(scope);
      });

      // Top level links close menu on click
      var links = section[0].querySelectorAll('li>a');
      angular.forEach(links, function(link) {
        var $link = angular.element(link);
        var li = closest($link, 'li');
        if (li.hasClass('has-dropdown') || li.hasClass('back') || li.hasClass('title')) {
          return;
        }

        $link.bind('click', function() {
          topBar.toggle(false);
        });

        scope.$on('$destroy', function() {
          $link.bind('click');
        });
      });
    }
  };
}])
.directive('hasDropdown', ['mediaQueries', function (mediaQueries) {
  return {
    scope: {},
    require: '^topBar',
    restrict: 'A',
    templateUrl: 'template/topbar/has-dropdown.html',
    replace: true,
    transclude: true,
    link: function(scope, element, attrs, topBar) {
      scope.triggerLink = element.children('a')[0];

      var $link = angular.element(scope.triggerLink);

      $link.bind('click', function(event) {
        topBar.forward(event);
      });
      scope.$on('$destroy', function() {
        $link.unbind('click');
      });

      element.bind('mouseenter', function() {
        if(topBar.settings.isHover && !mediaQueries.topbarBreakpoint()){
          element.addClass('not-click');
        }
      });
      element.bind('click', function(event) {
        if(!topBar.settings.isHover && !mediaQueries.topbarBreakpoint()){
          element.toggleClass('not-click');
        }
      });

      element.bind('mouseleave', function() {
        element.removeClass('not-click');
      });

      scope.$on('$destroy', function() {
        element.unbind('click');
        element.unbind('mouseenter');
        element.unbind('mouseleave');
      });
    },
    controller: ['$window', '$scope', function($window, $scope) {
      this.triggerLink = $scope.triggerLink;
    }]
  };
}])
.directive('topBarDropdown', ['$compile', function($compile) {
  return {
    scope: {},
    require: ['^topBar', '^hasDropdown'],
    restrict: 'A',
    replace: true,
    templateUrl: 'template/topbar/top-bar-dropdown.html',
    transclude: true,
    link: function(scope, element, attrs, ctrls) {
      var topBar = ctrls[0];
      var hasDropdown = ctrls[1];
      var $link = angular.element(hasDropdown.triggerLink);
      var url = $link.attr('href');
      var $titleLi;

      scope.linkText = $link.text();

      scope.back = function(event) {
        topBar.back(event);
      };

      // Add back link
      if (topBar.settings.customBackText) {
        scope.backText = topBar.settings.backText;
      } else {
        scope.backText = '&laquo; ' + $link.html();
      }

      if (topBar.settings.mobileShowParentLink && url && url.length > 1) {
        $titleLi = angular.element('<li class="title back js-generated">' +
            '<h5><a href="#" ng-click="back($event);">{{backText}}</a></h5></li>' +
            '<li><a class="parent-link js-generated" href="' +
            url + '">{{linkText}}</a></li>');
      } else {
        $titleLi = angular.element('<li class="title back js-generated">' +
            '<h5><a href="" ng-click="back($event);">{{backText}}</a></h5></li>');
      }

      $compile($titleLi)(scope);
      element.prepend($titleLi);
    }
  };
}]);

angular.module( 'mm.foundation.tour', [ 'mm.foundation.position', 'mm.foundation.tooltip' ] )

.service( '$tour', [ '$window', function ( $window ) {
  var currentIndex = getStoredCurrentStep();
  var ended = false;
  var steps = {};

  function getStoredCurrentStep() {
    try {
      return parseInt( $window.localStorage.getItem( 'mm.tour.step' ), 10 );
    } catch(e) {
      if (e.name !== "SecurityError") {
        throw e;
      }
    }
  }

  function storeCurrentStep() {
    try {
      $window.localStorage.setItem( 'mm.tour.step', currentIndex );
    } catch(e) {
      if (e.name !== "SecurityError") {
        throw e;
      }
    }
  }

  function setCurrentStep(step) {
    currentIndex = step;
    storeCurrentStep();
  }

  this.add = function ( index, attrs ) {
    steps[ index ] = attrs;
  };

  this.has = function ( index ) {
    return !!steps[ index ];
  };

  this.isActive = function () {
    return currentIndex > 0;
  };

  this.current = function ( index ) {
    if ( index ) {
      setCurrentStep( currentIndex );
    } else {
      return currentIndex;
    }
  };

  this.start = function () {
    setCurrentStep( 1 );
  };

  this.next = function () {
    setCurrentStep( currentIndex + 1 );
  };

  this.end = function () {
    setCurrentStep( 0 );
  };
}])

.directive( 'stepTextPopup', ['$tour', function ( $tour ) {
  return {
    restrict: 'EA',
    replace: true,
    scope: { title: '@', content: '@', placement: '@', animation: '&', isOpen: '&' },
    templateUrl: 'template/tour/tour.html',
    link: function (scope, element) {
      scope.isLastStep = function () {
        return !$tour.has( $tour.current() + 1 );
      };

      scope.endTour = function () {
        element.remove();
        $tour.end();
      };

      scope.nextStep = function () {
        element.remove();
        $tour.next();
      };

      scope.$on('$locationChangeSuccess', scope.endTour);
    }
  };
}])

.directive( 'stepText', [ '$position', '$tooltip', '$tour', '$window', function ( $position, $tooltip, $tour, $window ) {
  function isElementInViewport( element ) {
    var rect = element[0].getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= ($window.innerHeight - 80) &&
      rect.right <= $window.innerWidth
    );
  }

  function show( scope, element, attrs ) {
    var index = parseInt( attrs.stepIndex, 10);

    if ( $tour.isActive() && index ) {
      $tour.add( index, attrs );

      if ( index === $tour.current() ) {
        if ( !isElementInViewport( element ) ) {
          var offset = $position.offset( element );
          $window.scrollTo( 0, offset.top - $window.innerHeight / 2 );
        }

        return true;
      }
    }

    return false;
  }

  return $tooltip( 'stepText', 'step', show );
}]);

angular.module('mm.foundation.typeahead', ['mm.foundation.position', 'mm.foundation.bindHtml'])

/**
 * A helper service that can parse typeahead's syntax (string provided by users)
 * Extracted to a separate service for ease of unit testing
 */
  .factory('typeaheadParser', ['$parse', function ($parse) {

  //                      00000111000000000000022200000000000000003333333333333330000000000044000
  var TYPEAHEAD_REGEXP = /^\s*(.*?)(?:\s+as\s+(.*?))?\s+for\s+(?:([\$\w][\$\w\d]*))\s+in\s+(.*)$/;

  return {
    parse:function (input) {

      var match = input.match(TYPEAHEAD_REGEXP);
      if (!match) {
        throw new Error(
          "Expected typeahead specification in form of '_modelValue_ (as _label_)? for _item_ in _collection_'" +
            " but got '" + input + "'.");
      }

      return {
        itemName:match[3],
        source:$parse(match[4]),
        viewMapper:$parse(match[2] || match[1]),
        modelMapper:$parse(match[1])
      };
    }
  };
}])

  .directive('typeahead', ['$compile', '$parse', '$q', '$timeout', '$document', '$position', 'typeaheadParser',
    function ($compile, $parse, $q, $timeout, $document, $position, typeaheadParser) {

  var HOT_KEYS = [9, 13, 27, 38, 40];

  return {
    require:'ngModel',
    link:function (originalScope, element, attrs, modelCtrl) {

      //SUPPORTED ATTRIBUTES (OPTIONS)

      //minimal no of characters that needs to be entered before typeahead kicks-in
      var minSearch = originalScope.$eval(attrs.typeaheadMinLength) || 1;

      //minimal wait time after last character typed before typehead kicks-in
      var waitTime = originalScope.$eval(attrs.typeaheadWaitMs) || 0;

      //should it restrict model values to the ones selected from the popup only?
      var isEditable = originalScope.$eval(attrs.typeaheadEditable) !== false;

      //binding to a variable that indicates if matches are being retrieved asynchronously
      var isLoadingSetter = $parse(attrs.typeaheadLoading).assign || angular.noop;

      //a callback executed when a match is selected
      var onSelectCallback = $parse(attrs.typeaheadOnSelect);

      var inputFormatter = attrs.typeaheadInputFormatter ? $parse(attrs.typeaheadInputFormatter) : undefined;

      var appendToBody =  attrs.typeaheadAppendToBody ? $parse(attrs.typeaheadAppendToBody) : false;

      //INTERNAL VARIABLES

      //model setter executed upon match selection
      var $setModelValue = $parse(attrs.ngModel).assign;

      //expressions used by typeahead
      var parserResult = typeaheadParser.parse(attrs.typeahead);

      var hasFocus;

      //pop-up element used to display matches
      var popUpEl = angular.element('<div typeahead-popup></div>');
      popUpEl.attr({
        matches: 'matches',
        active: 'activeIdx',
        select: 'select(activeIdx)',
        query: 'query',
        position: 'position'
      });
      //custom item template
      if (angular.isDefined(attrs.typeaheadTemplateUrl)) {
        popUpEl.attr('template-url', attrs.typeaheadTemplateUrl);
      }

      //create a child scope for the typeahead directive so we are not polluting original scope
      //with typeahead-specific data (matches, query etc.)
      var scope = originalScope.$new();
      originalScope.$on('$destroy', function(){
        scope.$destroy();
      });

      var resetMatches = function() {
        scope.matches = [];
        scope.activeIdx = -1;
      };

      var getMatchesAsync = function(inputValue) {

        var locals = {$viewValue: inputValue};
        isLoadingSetter(originalScope, true);
        $q.when(parserResult.source(originalScope, locals)).then(function(matches) {

          //it might happen that several async queries were in progress if a user were typing fast
          //but we are interested only in responses that correspond to the current view value
          if (inputValue === modelCtrl.$viewValue && hasFocus) {
            if (matches.length > 0) {

              scope.activeIdx = 0;
              scope.matches.length = 0;

              //transform labels
              for(var i=0; i<matches.length; i++) {
                locals[parserResult.itemName] = matches[i];
                scope.matches.push({
                  label: parserResult.viewMapper(scope, locals),
                  model: matches[i]
                });
              }

              scope.query = inputValue;
              //position pop-up with matches - we need to re-calculate its position each time we are opening a window
              //with matches as a pop-up might be absolute-positioned and position of an input might have changed on a page
              //due to other elements being rendered
              scope.position = appendToBody ? $position.offset(element) : $position.position(element);
              scope.position.top = scope.position.top + element.prop('offsetHeight');

            } else {
              resetMatches();
            }
          }
        }, function(){
          resetMatches();
        }).finally(function() {
          isLoadingSetter(originalScope, false);
        });
      };

      resetMatches();

      //we need to propagate user's query so we can higlight matches
      scope.query = undefined;

      //Declare the timeout promise var outside the function scope so that stacked calls can be cancelled later
      var timeoutPromise;

      //plug into $parsers pipeline to open a typeahead on view changes initiated from DOM
      //$parsers kick-in on all the changes coming from the view as well as manually triggered by $setViewValue
      modelCtrl.$parsers.unshift(function (inputValue) {

        if (inputValue && inputValue.length >= minSearch) {
          if (waitTime > 0) {
            if (timeoutPromise) {
              $timeout.cancel(timeoutPromise);//cancel previous timeout
            }
            timeoutPromise = $timeout(function () {
              getMatchesAsync(inputValue);
            }, waitTime);
          } else {
            getMatchesAsync(inputValue);
          }
        } else {
          isLoadingSetter(originalScope, false);
          resetMatches();
        }

        if (isEditable) {
          return inputValue;
        } else {
          if (!inputValue) {
            // Reset in case user had typed something previously.
            modelCtrl.$setValidity('editable', true);
            return inputValue;
          } else {
            modelCtrl.$setValidity('editable', false);
            return undefined;
          }
        }
      });

      modelCtrl.$formatters.push(function (modelValue) {

        var candidateViewValue, emptyViewValue;
        var locals = {};

        if (inputFormatter) {

          locals['$model'] = modelValue;
          return inputFormatter(originalScope, locals);

        } else {

          //it might happen that we don't have enough info to properly render input value
          //we need to check for this situation and simply return model value if we can't apply custom formatting
          locals[parserResult.itemName] = modelValue;
          candidateViewValue = parserResult.viewMapper(originalScope, locals);
          locals[parserResult.itemName] = undefined;
          emptyViewValue = parserResult.viewMapper(originalScope, locals);

          return candidateViewValue!== emptyViewValue ? candidateViewValue : modelValue;
        }
      });

      scope.select = function (activeIdx) {
        //called from within the $digest() cycle
        var locals = {};
        var model, item;

        locals[parserResult.itemName] = item = scope.matches[activeIdx].model;
        model = parserResult.modelMapper(originalScope, locals);
        $setModelValue(originalScope, model);
        modelCtrl.$setValidity('editable', true);

        onSelectCallback(originalScope, {
          $item: item,
          $model: model,
          $label: parserResult.viewMapper(originalScope, locals)
        });

        resetMatches();

        //return focus to the input element if a mach was selected via a mouse click event
        element[0].focus();
      };

      //bind keyboard events: arrows up(38) / down(40), enter(13) and tab(9), esc(27)
      element.bind('keydown', function (evt) {

        //typeahead is open and an "interesting" key was pressed
        if (scope.matches.length === 0 || HOT_KEYS.indexOf(evt.which) === -1) {
          return;
        }

        evt.preventDefault();

        if (evt.which === 40) {
          scope.activeIdx = (scope.activeIdx + 1) % scope.matches.length;
          scope.$digest();

        } else if (evt.which === 38) {
          scope.activeIdx = (scope.activeIdx ? scope.activeIdx : scope.matches.length) - 1;
          scope.$digest();

        } else if (evt.which === 13 || evt.which === 9) {
          scope.$apply(function () {
            scope.select(scope.activeIdx);
          });

        } else if (evt.which === 27) {
          evt.stopPropagation();

          resetMatches();
          scope.$digest();
        }
      });

      element.bind('blur', function (evt) {
        hasFocus = false;
      });

      element.bind('focus', function (evt) {
        hasFocus = true;
      });

      // Keep reference to click handler to unbind it.
      var dismissClickHandler = function (evt) {
        if (element[0] !== evt.target) {
          resetMatches();
          scope.$digest();
        }
      };

      $document.bind('click', dismissClickHandler);

      originalScope.$on('$destroy', function(){
        $document.unbind('click', dismissClickHandler);
      });

      var $popup = $compile(popUpEl)(scope);
      if ( appendToBody ) {
        $document.find('body').append($popup);
      } else {
        element.after($popup);
      }
    }
  };

}])

  .directive('typeaheadPopup', function () {
    return {
      restrict:'EA',
      scope:{
        matches:'=',
        query:'=',
        active:'=',
        position:'=',
        select:'&'
      },
      replace:true,
      templateUrl:'template/typeahead/typeahead-popup.html',
      link:function (scope, element, attrs) {

        scope.templateUrl = attrs.templateUrl;

        scope.isOpen = function () {
          return scope.matches.length > 0;
        };

        scope.isActive = function (matchIdx) {
          return scope.active == matchIdx;
        };

        scope.selectActive = function (matchIdx) {
          scope.active = matchIdx;
        };

        scope.selectMatch = function (activeIdx) {
          scope.select({activeIdx:activeIdx});
        };
      }
    };
  })

  .directive('typeaheadMatch', ['$http', '$templateCache', '$compile', '$parse', function ($http, $templateCache, $compile, $parse) {
    return {
      restrict:'EA',
      scope:{
        index:'=',
        match:'=',
        query:'='
      },
      link:function (scope, element, attrs) {
        var tplUrl = $parse(attrs.templateUrl)(scope.$parent) || 'template/typeahead/typeahead-match.html';
        $http.get(tplUrl, {cache: $templateCache}).success(function(tplContent){
           element.replaceWith($compile(tplContent.trim())(scope));
        });
      }
    };
  }])

  .filter('typeaheadHighlight', function() {

    function escapeRegexp(queryToEscape) {
      return queryToEscape.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
    }

    return function(matchItem, query) {
      return query ? matchItem.replace(new RegExp(escapeRegexp(query), 'gi'), '<strong>$&</strong>') : matchItem;
    };
  });

angular.module("template/accordion/accordion-group.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/accordion/accordion-group.html",
    "<dd>\n" +
    "  <a ng-click=\"isOpen = !isOpen\" ng-class=\"{ active: isOpen }\"  accordion-transclude=\"heading\">{{heading}}</a>\n" +
    "  <div class=\"content\" ng-class=\"{ active: isOpen }\" ng-transclude></div>\n" +
    "</dd>\n" +
    "");
}]);

angular.module("template/accordion/accordion.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/accordion/accordion.html",
    "<dl class=\"accordion\" ng-transclude></dl>\n" +
    "");
}]);

angular.module("template/alert/alert.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/alert/alert.html",
    "<div class='alert-box' ng-class='(type || \"\")'>\n" +
    "  <span ng-transclude></span>\n" +
    "  <a ng-show='closeable' class='close' ng-click='close()'>&times;</a>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/modal/backdrop.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/modal/backdrop.html",
    "<div class=\"reveal-modal-bg fade\" ng-class=\"{in: animate}\" ng-click=\"close($event)\" style=\"display: block\"></div>\n" +
    "");
}]);

angular.module("template/modal/window.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/modal/window.html",
    "<div tabindex=\"-1\" class=\"reveal-modal fade {{ windowClass }}\"\n" +
    "  ng-class=\"{in: animate}\" style=\"display: block; visibility: visible\">\n" +
    "  <div ng-transclude></div>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/pagination/pager.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/pagination/pager.html",
    "<ul class=\"pagination\">\n" +
    "  <li ng-repeat=\"page in pages\" class=\"arrow\" ng-class=\"{unavailable: page.disabled, left: page.previous, right: page.next}\"><a ng-click=\"selectPage(page.number)\">{{page.text}}</a></li>\n" +
    "</ul>\n" +
    "");
}]);

angular.module("template/pagination/pagination.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/pagination/pagination.html",
    "<ul class=\"pagination\">\n" +
    "  <li ng-repeat=\"page in pages\" ng-class=\"{arrow: $first || $last, current: page.active, unavailable: page.disabled}\"><a ng-click=\"selectPage(page.number)\">{{page.text}}</a></li>\n" +
    "</ul>\n" +
    "");
}]);

angular.module("template/tooltip/tooltip-html-unsafe-popup.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/tooltip/tooltip-html-unsafe-popup.html",
    "<span class=\"tooltip tip-{{placement}}\"\n" +
    "  ng-class=\"{ in: isOpen(), fade: animation() }\"\n" +
    "  style=\"width: auto\">\n" +
    "  <span bind-html-unsafe=\"content\"></span>\n" +
    "  <span class=\"nub\"></span>\n" +
    "</span>\n" +
    "");
}]);

angular.module("template/tooltip/tooltip-popup.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/tooltip/tooltip-popup.html",
    "<span class=\"tooltip tip-{{placement}}\"\n" +
    "  ng-class=\"{ in: isOpen(), fade: animation() }\"\n" +
    "  style=\"width: auto\">\n" +
    "  <span ng-bind=\"content\"></span>\n" +
    "  <span class=\"nub\"></span>\n" +
    "</span>\n" +
    "");
}]);

angular.module("template/popover/popover.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/popover/popover.html",
    "<div class=\"joyride-tip-guide\" ng-class=\"{ in: isOpen(), fade: animation() }\">\n" +
    "  <span class=\"joyride-nub\" ng-class=\"{\n" +
    "    bottom: placement === 'top',\n" +
    "    left: placement === 'right',\n" +
    "    right: placement === 'left',\n" +
    "    top: placement === 'bottom'\n" +
    "  }\"></span>\n" +
    "  <div class=\"joyride-content-wrapper\">\n" +
    "    <h4 ng-bind=\"title\" ng-show=\"title\"></h4>\n" +
    "    <p ng-bind=\"content\"></p>\n" +
    "  </div>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/progressbar/bar.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/progressbar/bar.html",
    "<span class=\"meter\" ng-transclude></span>\n" +
    "");
}]);

angular.module("template/progressbar/progress.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/progressbar/progress.html",
    "<div class=\"progress\" ng-class=\"type\" ng-transclude></div>\n" +
    "");
}]);

angular.module("template/progressbar/progressbar.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/progressbar/progressbar.html",
    "<div class=\"progress\" ng-class=\"type\">\n" +
    "  <span class=\"meter\" ng-transclude></span>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/rating/rating.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/rating/rating.html",
    "<span ng-mouseleave=\"reset()\">\n" +
    "  <i ng-repeat=\"r in range\" ng-mouseenter=\"enter($index + 1)\" ng-click=\"rate($index + 1)\" class=\"fa\"\n" +
    "    ng-class=\"$index < val && (r.stateOn || 'fa-star') || (r.stateOff || 'fa-star-o')\"></i>\n" +
    "</span>\n" +
    "");
}]);

angular.module("template/tabs/tab.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/tabs/tab.html",
    "<dd ng-class=\"{active: active}\">\n" +
    "  <a ng-click=\"select()\" tab-heading-transclude>{{heading}}</a>\n" +
    "</dd>\n" +
    "");
}]);

angular.module("template/tabs/tabset.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/tabs/tabset.html",
    "<div class=\"tabbable\">\n" +
    "  <dl class=\"tabs\" ng-class=\"{'vertical': vertical}\" ng-transclude></dl>\n" +
    "  <div class=\"tabs-content\" ng-class=\"{'vertical': vertical}\">\n" +
    "    <div class=\"content\" \n" +
    "      ng-repeat=\"tab in tabs\" \n" +
    "      ng-class=\"{active: tab.active}\">\n" +
    "      <div tab-content-transclude=\"tab\"></div>\n" +
    "    </div>\n" +
    "  </div>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/topbar/has-dropdown.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/topbar/has-dropdown.html",
    "<li class=\"has-dropdown\" ng-transclude></li>");
}]);

angular.module("template/topbar/toggle-top-bar.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/topbar/toggle-top-bar.html",
    "<li class=\"toggle-topbar menu-icon\" ng-transclude></li>");
}]);

angular.module("template/topbar/top-bar-dropdown.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/topbar/top-bar-dropdown.html",
    "<ul class=\"dropdown\" ng-transclude></ul>");
}]);

angular.module("template/topbar/top-bar-section.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/topbar/top-bar-section.html",
    "<section class=\"top-bar-section\" ng-transclude></section>");
}]);

angular.module("template/topbar/top-bar.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/topbar/top-bar.html",
    "<nav class=\"top-bar\" ng-transclude></nav>");
}]);

angular.module("template/tour/tour.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/tour/tour.html",
    "<div class=\"joyride-tip-guide\" ng-class=\"{ in: isOpen(), fade: animation() }\">\n" +
    "  <span class=\"joyride-nub\" ng-class=\"{\n" +
    "    bottom: placement === 'top',\n" +
    "    left: placement === 'right',\n" +
    "    right: placement === 'left',\n" +
    "    top: placement === 'bottom'\n" +
    "  }\"></span>\n" +
    "  <div class=\"joyride-content-wrapper\">\n" +
    "    <h4 ng-bind=\"title\" ng-show=\"title\"></h4>\n" +
    "    <p ng-bind=\"content\"></p>\n" +
    "    <a class=\"small button joyride-next-tip\" ng-show=\"!isLastStep()\" ng-click=\"nextStep()\">Next</a>\n" +
    "    <a class=\"small button joyride-next-tip\" ng-show=\"isLastStep()\" ng-click=\"endTour()\">End</a>\n" +
    "    <a class=\"joyride-close-tip\" ng-click=\"endTour()\">&times;</a>\n" +
    "  </div>\n" +
    "</div>\n" +
    "");
}]);

angular.module("template/typeahead/typeahead-match.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/typeahead/typeahead-match.html",
    "<a tabindex=\"-1\" bind-html-unsafe=\"match.label | typeaheadHighlight:query\"></a>");
}]);

angular.module("template/typeahead/typeahead-popup.html", []).run(["$templateCache", function($templateCache) {
  $templateCache.put("template/typeahead/typeahead-popup.html",
    "<ul class=\"f-dropdown\" ng-style=\"{display: isOpen()&&'block' || 'none', top: position.top+'px', left: position.left+'px'}\">\n" +
    "    <li ng-repeat=\"match in matches\" ng-class=\"{active: isActive($index) }\" ng-mouseenter=\"selectActive($index)\" ng-click=\"selectMatch($index)\">\n" +
    "        <div typeahead-match index=\"$index\" match=\"match\" query=\"query\" template-url=\"templateUrl\"></div>\n" +
    "    </li>\n" +
    "</ul>\n" +
    "");
}]);

(function() {
  'use strict';

  angular.module('toastr', [])
    .factory('toastr', toastr);

  toastr.$inject = ['$animate', '$injector', '$document', '$rootScope', '$sce', 'toastrConfig', '$q'];

  function toastr($animate, $injector, $document, $rootScope, $sce, toastrConfig, $q) {
    var container;
    var index = 0;
    var toasts = [];

    var previousToastMessage = '';
    var openToasts = {};

    var containerDefer = $q.defer();

    var toast = {
      active: active,
      clear: clear,
      error: error,
      info: info,
      remove: remove,
      success: success,
      warning: warning
    };

    return toast;

    /* Public API */
    function active() {
      return toasts.length;
    }

    function clear(toast) {
      // Bit of a hack, I will remove this soon with a BC
      if (arguments.length === 1 && !toast) { return; }

      if (toast) {
        remove(toast.toastId);
      } else {
        for (var i = 0; i < toasts.length; i++) {
          remove(toasts[i].toastId);
        }
      }
    }

    function error(message, title, optionsOverride) {
      var type = _getOptions().iconClasses.error;
      return _buildNotification(type, message, title, optionsOverride);
    }

    function info(message, title, optionsOverride) {
      var type = _getOptions().iconClasses.info;
      return _buildNotification(type, message, title, optionsOverride);
    }

    function success(message, title, optionsOverride) {
      var type = _getOptions().iconClasses.success;
      return _buildNotification(type, message, title, optionsOverride);
    }

    function warning(message, title, optionsOverride) {
      var type = _getOptions().iconClasses.warning;
      return _buildNotification(type, message, title, optionsOverride);
    }

    function remove(toastId, wasClicked) {
      var toast = findToast(toastId);

      if (toast && ! toast.deleting) { // Avoid clicking when fading out
        toast.deleting = true;
        toast.isOpened = false;
        $animate.leave(toast.el).then(function() {
          if (toast.scope.options.onHidden) {
            toast.scope.options.onHidden(!!wasClicked, toast);
          }
          toast.scope.$destroy();
          var index = toasts.indexOf(toast);
          delete openToasts[toast.scope.message];
          toasts.splice(index, 1);
          var maxOpened = toastrConfig.maxOpened;
          if (maxOpened && toasts.length >= maxOpened) {
            toasts[maxOpened - 1].open.resolve();
          }
          if (lastToast()) {
            container.remove();
            container = null;
            containerDefer = $q.defer();
          }
        });
      }

      function findToast(toastId) {
        for (var i = 0; i < toasts.length; i++) {
          if (toasts[i].toastId === toastId) {
            return toasts[i];
          }
        }
      }

      function lastToast() {
        return !toasts.length;
      }
    }

    /* Internal functions */
    function _buildNotification(type, message, title, optionsOverride) {
      if (angular.isObject(title)) {
        optionsOverride = title;
        title = null;
      }

      return _notify({
        iconClass: type,
        message: message,
        optionsOverride: optionsOverride,
        title: title
      });
    }

    function _getOptions() {
      return angular.extend({}, toastrConfig);
    }

    function _createOrGetContainer(options) {
      if(container) { return containerDefer.promise; }

      container = angular.element('<div></div>');
      container.attr('id', options.containerId);
      container.addClass(options.positionClass);
      container.css({'pointer-events': 'auto'});

      var target = angular.element(document.querySelector(options.target));

      if ( ! target || ! target.length) {
        throw 'Target for toasts doesn\'t exist';
      }

      $animate.enter(container, target).then(function() {
        containerDefer.resolve();
      });

      return containerDefer.promise;
    }

    function _notify(map) {
      var options = _getOptions();

      if (shouldExit()) { return; }

      var newToast = createToast();

      toasts.push(newToast);

      if (ifMaxOpenedAndAutoDismiss()) {
        var oldToasts = toasts.slice(0, (toasts.length - options.maxOpened));
        for (var i = 0, len = oldToasts.length; i < len; i++) {
          remove(oldToasts[i].toastId);
        }
      }

      if (maxOpenedNotReached()) {
        newToast.open.resolve();
      }

      newToast.open.promise.then(function() {
        _createOrGetContainer(options).then(function() {
          newToast.isOpened = true;
          if (options.newestOnTop) {
            $animate.enter(newToast.el, container).then(function() {
              newToast.scope.init();
            });
          } else {
            var sibling = container[0].lastChild ? angular.element(container[0].lastChild) : null;
            $animate.enter(newToast.el, container, sibling).then(function() {
              newToast.scope.init();
            });
          }
        });
      });

      return newToast;

      function ifMaxOpenedAndAutoDismiss() {
        return options.autoDismiss && options.maxOpened && toasts.length > options.maxOpened;
      }

      function createScope(toast, map, options) {
        if (options.allowHtml) {
          toast.scope.allowHtml = true;
          toast.scope.title = $sce.trustAsHtml(map.title);
          toast.scope.message = $sce.trustAsHtml(map.message);
        } else {
          toast.scope.title = map.title;
          toast.scope.message = map.message;
        }

        toast.scope.toastType = toast.iconClass;
        toast.scope.toastId = toast.toastId;
        toast.scope.extraData = options.extraData;

        toast.scope.options = {
          extendedTimeOut: options.extendedTimeOut,
          messageClass: options.messageClass,
          onHidden: options.onHidden,
          onShown: generateEvent('onShown'),
          onTap: generateEvent('onTap'),
          progressBar: options.progressBar,
          tapToDismiss: options.tapToDismiss,
          timeOut: options.timeOut,
          titleClass: options.titleClass,
          toastClass: options.toastClass
        };

        if (options.closeButton) {
          toast.scope.options.closeHtml = options.closeHtml;
        }

        function generateEvent(event) {
          if (options[event]) {
            return function() {
              options[event](toast);
            };
          }
        }
      }

      function createToast() {
        var newToast = {
          toastId: index++,
          isOpened: false,
          scope: $rootScope.$new(),
          open: $q.defer()
        };
        newToast.iconClass = map.iconClass;
        if (map.optionsOverride) {
          angular.extend(options, cleanOptionsOverride(map.optionsOverride));
          newToast.iconClass = map.optionsOverride.iconClass || newToast.iconClass;
        }

        createScope(newToast, map, options);

        newToast.el = createToastEl(newToast.scope);

        return newToast;

        function cleanOptionsOverride(options) {
          var badOptions = ['containerId', 'iconClasses', 'maxOpened', 'newestOnTop',
                            'positionClass', 'preventDuplicates', 'preventOpenDuplicates', 'templates'];
          for (var i = 0, l = badOptions.length; i < l; i++) {
            delete options[badOptions[i]];
          }

          return options;
        }
      }

      function createToastEl(scope) {
        var angularDomEl = angular.element('<div toast></div>'),
          $compile = $injector.get('$compile');
        return $compile(angularDomEl)(scope);
      }

      function maxOpenedNotReached() {
        return options.maxOpened && toasts.length <= options.maxOpened || !options.maxOpened;
      }

      function shouldExit() {
        var isDuplicateOfLast = options.preventDuplicates && map.message === previousToastMessage;
        var isDuplicateOpen = options.preventOpenDuplicates && openToasts[map.message];

        if (isDuplicateOfLast || isDuplicateOpen) {
          return true;
        }

        previousToastMessage = map.message;
        openToasts[map.message] = true;

        return false;
      }
    }
  }
}());

(function() {
  'use strict';

  angular.module('toastr')
    .constant('toastrConfig', {
      allowHtml: false,
      autoDismiss: false,
      closeButton: false,
      closeHtml: '<button>&times;</button>',
      containerId: 'toast-container',
      extendedTimeOut: 1000,
      iconClasses: {
        error: 'toast-error',
        info: 'toast-info',
        success: 'toast-success',
        warning: 'toast-warning'
      },
      maxOpened: 0,
      messageClass: 'toast-message',
      newestOnTop: true,
      onHidden: null,
      onShown: null,
      onTap: null,
      positionClass: 'toast-top-right',
      preventDuplicates: false,
      preventOpenDuplicates: false,
      progressBar: false,
      tapToDismiss: true,
      target: 'body',
      templates: {
        toast: 'directives/toast/toast.html',
        progressbar: 'directives/progressbar/progressbar.html'
      },
      timeOut: 5000,
      titleClass: 'toast-title',
      toastClass: 'toast'
    });
}());

(function() {
  'use strict';

  angular.module('toastr')
    .directive('progressBar', progressBar);

  progressBar.$inject = ['toastrConfig'];

  function progressBar(toastrConfig) {
    return {
      replace: true,
      require: '^toast',
      templateUrl: function() {
        return toastrConfig.templates.progressbar;
      },
      link: linkFunction
    };

    function linkFunction(scope, element, attrs, toastCtrl) {
      var intervalId, currentTimeOut, hideTime;

      toastCtrl.progressBar = scope;

      scope.start = function(duration) {
        if (intervalId) {
          clearInterval(intervalId);
        }

        currentTimeOut = parseFloat(duration);
        hideTime = new Date().getTime() + currentTimeOut;
        intervalId = setInterval(updateProgress, 10);
      };

      scope.stop = function() {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };

      function updateProgress() {
        var percentage = ((hideTime - (new Date().getTime())) / currentTimeOut) * 100;
        element.css('width', percentage + '%');
      }

      scope.$on('$destroy', function() {
        // Failsafe stop
        clearInterval(intervalId);
      });
    }
  }
}());

(function() {
  'use strict';

  angular.module('toastr')
    .controller('ToastController', ToastController);

  function ToastController() {
    this.progressBar = null;

    this.startProgressBar = function(duration) {
      if (this.progressBar) {
        this.progressBar.start(duration);
      }
    };

    this.stopProgressBar = function() {
      if (this.progressBar) {
        this.progressBar.stop();
      }
    };
  }
}());

(function() {
  'use strict';

  angular.module('toastr')
    .directive('toast', toast);

  toast.$inject = ['$injector', '$interval', 'toastrConfig', 'toastr'];

  function toast($injector, $interval, toastrConfig, toastr) {
    return {
      replace: true,
      templateUrl: function() {
        return toastrConfig.templates.toast;
      },
      controller: 'ToastController',
      link: toastLinkFunction
    };

    function toastLinkFunction(scope, element, attrs, toastCtrl) {
      var timeout;

      scope.toastClass = scope.options.toastClass;
      scope.titleClass = scope.options.titleClass;
      scope.messageClass = scope.options.messageClass;
      scope.progressBar = scope.options.progressBar;

      if (wantsCloseButton()) {
        var button = angular.element(scope.options.closeHtml),
          $compile = $injector.get('$compile');
        button.addClass('toast-close-button');
        button.attr('ng-click', 'close(true, $event)');
        $compile(button)(scope);
        element.prepend(button);
      }

      scope.init = function() {
        if (scope.options.timeOut) {
          timeout = createTimeout(scope.options.timeOut);
        }
        if (scope.options.onShown) {
          scope.options.onShown();
        }
      };

      element.on('mouseenter', function() {
        hideAndStopProgressBar();
        if (timeout) {
          $interval.cancel(timeout);
        }
      });

      scope.tapToast = function () {
        if (angular.isFunction(scope.options.onTap)) {
          scope.options.onTap();
        }
        if (scope.options.tapToDismiss) {
          scope.close(true);
        }
      };

      scope.close = function (wasClicked, $event) {
        if ($event && angular.isFunction($event.stopPropagation)) {
          $event.stopPropagation();
        }
        toastr.remove(scope.toastId, wasClicked);
      };

      element.on('mouseleave', function() {
        if (scope.options.timeOut === 0 && scope.options.extendedTimeOut === 0) { return; }
        scope.$apply(function() {
          scope.progressBar = scope.options.progressBar;
        });
        timeout = createTimeout(scope.options.extendedTimeOut);
      });

      function createTimeout(time) {
        toastCtrl.startProgressBar(time);
        return $interval(function() {
          toastCtrl.stopProgressBar();
          toastr.remove(scope.toastId);
        }, time, 1);
      }

      function hideAndStopProgressBar() {
        scope.progressBar = false;
        toastCtrl.stopProgressBar();
      }

      function wantsCloseButton() {
        return scope.options.closeHtml;
      }
    }
  }
}());

angular.module("toastr").run(["$templateCache", function($templateCache) {$templateCache.put("directives/progressbar/progressbar.html","<div class=\"toast-progress\"></div>\n");
$templateCache.put("directives/toast/toast.html","<div class=\"{{toastClass}} {{toastType}}\" ng-click=\"tapToast()\">\n  <div ng-switch on=\"allowHtml\">\n    <div ng-switch-default ng-if=\"title\" class=\"{{titleClass}}\" aria-label=\"{{title}}\">{{title}}</div>\n    <div ng-switch-default class=\"{{messageClass}}\" aria-label=\"{{message}}\">{{message}}</div>\n    <div ng-switch-when=\"true\" ng-if=\"title\" class=\"{{titleClass}}\" ng-bind-html=\"title\"></div>\n    <div ng-switch-when=\"true\" class=\"{{messageClass}}\" ng-bind-html=\"message\"></div>\n  </div>\n  <progress-bar ng-if=\"progressBar\"></progress-bar>\n</div>\n");}]);
!function(e,a){"object"==typeof exports&&"undefined"!=typeof module?module.exports=a():"function"==typeof define&&define.amd?define(a):e.moment=a()}(this,function(){"use strict";var E;function M(){return E.apply(null,arguments)}function F(e){return e instanceof Array||"[object Array]"===Object.prototype.toString.call(e)}function z(e){return null!=e&&"[object Object]"===Object.prototype.toString.call(e)}function l(e,a){return Object.prototype.hasOwnProperty.call(e,a)}function N(e){if(Object.getOwnPropertyNames)return 0===Object.getOwnPropertyNames(e).length;for(var a in e)if(l(e,a))return;return 1}function d(e){return void 0===e}function J(e){return"number"==typeof e||"[object Number]"===Object.prototype.toString.call(e)}function R(e){return e instanceof Date||"[object Date]"===Object.prototype.toString.call(e)}function C(e,a){for(var t=[],s=e.length,n=0;n<s;++n)t.push(a(e[n],n));return t}function I(e,a){for(var t in a)l(a,t)&&(e[t]=a[t]);return l(a,"toString")&&(e.toString=a.toString),l(a,"valueOf")&&(e.valueOf=a.valueOf),e}function U(e,a,t,s){return Fa(e,a,t,s,!0).utc()}function h(e){return null==e._pf&&(e._pf={empty:!1,unusedTokens:[],unusedInput:[],overflow:-2,charsLeftOver:0,nullInput:!1,invalidEra:null,invalidMonth:null,invalidFormat:!1,userInvalidated:!1,iso:!1,parsedDateParts:[],era:null,meridiem:null,rfc2822:!1,weekdayMismatch:!1}),e._pf}function G(e){if(null==e._isValid){var a=h(e),t=B.call(a.parsedDateParts,function(e){return null!=e}),t=!isNaN(e._d.getTime())&&a.overflow<0&&!a.empty&&!a.invalidEra&&!a.invalidMonth&&!a.invalidWeekday&&!a.weekdayMismatch&&!a.nullInput&&!a.invalidFormat&&!a.userInvalidated&&(!a.meridiem||a.meridiem&&t);if(e._strict&&(t=t&&0===a.charsLeftOver&&0===a.unusedTokens.length&&void 0===a.bigHour),null!=Object.isFrozen&&Object.isFrozen(e))return t;e._isValid=t}return e._isValid}function V(e){var a=U(NaN);return null!=e?I(h(a),e):h(a).userInvalidated=!0,a}var B=Array.prototype.some||function(e){for(var a=Object(this),t=a.length>>>0,s=0;s<t;s++)if(s in a&&e.call(this,a[s],s,a))return!0;return!1},K=M.momentProperties=[],q=!1;function Z(e,a){var t,s,n,r=K.length;if(d(a._isAMomentObject)||(e._isAMomentObject=a._isAMomentObject),d(a._i)||(e._i=a._i),d(a._f)||(e._f=a._f),d(a._l)||(e._l=a._l),d(a._strict)||(e._strict=a._strict),d(a._tzm)||(e._tzm=a._tzm),d(a._isUTC)||(e._isUTC=a._isUTC),d(a._offset)||(e._offset=a._offset),d(a._pf)||(e._pf=h(a)),d(a._locale)||(e._locale=a._locale),0<r)for(t=0;t<r;t++)d(n=a[s=K[t]])||(e[s]=n);return e}function $(e){Z(this,e),this._d=new Date(null!=e._d?e._d.getTime():NaN),this.isValid()||(this._d=new Date(NaN)),!1===q&&(q=!0,M.updateOffset(this),q=!1)}function Q(e){return e instanceof $||null!=e&&null!=e._isAMomentObject}function X(e){!1===M.suppressDeprecationWarnings&&"undefined"!=typeof console&&console.warn&&console.warn("Deprecation warning: "+e)}function e(r,d){var i=!0;return I(function(){if(null!=M.deprecationHandler&&M.deprecationHandler(null,r),i){for(var e,a,t=[],s=arguments.length,n=0;n<s;n++){if(e="","object"==typeof arguments[n]){for(a in e+="\n["+n+"] ",arguments[0])l(arguments[0],a)&&(e+=a+": "+arguments[0][a]+", ");e=e.slice(0,-2)}else e=arguments[n];t.push(e)}X(r+"\nArguments: "+Array.prototype.slice.call(t).join("")+"\n"+(new Error).stack),i=!1}return d.apply(this,arguments)},d)}var ee={};function ae(e,a){null!=M.deprecationHandler&&M.deprecationHandler(e,a),ee[e]||(X(a),ee[e]=!0)}function te(e){return"undefined"!=typeof Function&&e instanceof Function||"[object Function]"===Object.prototype.toString.call(e)}function se(e,a){var t,s=I({},e);for(t in a)l(a,t)&&(z(e[t])&&z(a[t])?(s[t]={},I(s[t],e[t]),I(s[t],a[t])):null!=a[t]?s[t]=a[t]:delete s[t]);for(t in e)l(e,t)&&!l(a,t)&&z(e[t])&&(s[t]=I({},s[t]));return s}function ne(e){null!=e&&this.set(e)}M.suppressDeprecationWarnings=!1,M.deprecationHandler=null;var re=Object.keys||function(e){var a,t=[];for(a in e)l(e,a)&&t.push(a);return t};function de(e,a,t){var s=""+Math.abs(e);return(0<=e?t?"+":"":"-")+Math.pow(10,Math.max(0,a-s.length)).toString().substr(1)+s}var ie=/(\[[^\[]*\])|(\\)?([Hh]mm(ss)?|Mo|MM?M?M?|Do|DDDo|DD?D?D?|ddd?d?|do?|w[o|w]?|W[o|W]?|Qo?|N{1,5}|YYYYYY|YYYYY|YYYY|YY|y{2,4}|yo?|gg(ggg?)?|GG(GGG?)?|e|E|a|A|hh?|HH?|kk?|mm?|ss?|S{1,9}|x|X|zz?|ZZ?|.)/g,_e=/(\[[^\[]*\])|(\\)?(LTS|LT|LL?L?L?|l{1,4})/g,oe={},me={};function s(e,a,t,s){var n="string"==typeof s?function(){return this[s]()}:s;e&&(me[e]=n),a&&(me[a[0]]=function(){return de(n.apply(this,arguments),a[1],a[2])}),t&&(me[t]=function(){return this.localeData().ordinal(n.apply(this,arguments),e)})}function ue(e,a){return e.isValid()?(a=le(a,e.localeData()),oe[a]=oe[a]||function(s){for(var e,n=s.match(ie),a=0,r=n.length;a<r;a++)me[n[a]]?n[a]=me[n[a]]:n[a]=(e=n[a]).match(/\[[\s\S]/)?e.replace(/^\[|\]$/g,""):e.replace(/\\/g,"");return function(e){for(var a="",t=0;t<r;t++)a+=te(n[t])?n[t].call(e,s):n[t];return a}}(a),oe[a](e)):e.localeData().invalidDate()}function le(e,a){var t=5;function s(e){return a.longDateFormat(e)||e}for(_e.lastIndex=0;0<=t&&_e.test(e);)e=e.replace(_e,s),_e.lastIndex=0,--t;return e}var Me={};function a(e,a){var t=e.toLowerCase();Me[t]=Me[t+"s"]=Me[a]=e}function i(e){return"string"==typeof e?Me[e]||Me[e.toLowerCase()]:void 0}function he(e){var a,t,s={};for(t in e)l(e,t)&&(a=i(t))&&(s[a]=e[t]);return s}var ce={};function t(e,a){ce[e]=a}function Le(e){return e%4==0&&e%100!=0||e%400==0}function u(e){return e<0?Math.ceil(e)||0:Math.floor(e)}function m(e){var e=+e,a=0;return a=0!=e&&isFinite(e)?u(e):a}function Ye(a,t){return function(e){return null!=e?(fe(this,a,e),M.updateOffset(this,t),this):ye(this,a)}}function ye(e,a){return e.isValid()?e._d["get"+(e._isUTC?"UTC":"")+a]():NaN}function fe(e,a,t){e.isValid()&&!isNaN(t)&&("FullYear"===a&&Le(e.year())&&1===e.month()&&29===e.date()?(t=m(t),e._d["set"+(e._isUTC?"UTC":"")+a](t,e.month(),ze(t,e.month()))):e._d["set"+(e._isUTC?"UTC":"")+a](t))}var n=/\d/,r=/\d\d/,pe=/\d{3}/,ke=/\d{4}/,_=/[+-]?\d{6}/,o=/\d\d?/,c=/\d\d\d\d?/,L=/\d\d\d\d\d\d?/,De=/\d{1,3}/,Y=/\d{1,4}/,y=/[+-]?\d{1,6}/,Te=/\d+/,ge=/[+-]?\d+/,we=/Z|[+-]\d\d:?\d\d/gi,be=/Z|[+-]\d\d(?::?\d\d)?/gi,f=/[0-9]{0,256}['a-z\u00A0-\u05FF\u0700-\uD7FF\uF900-\uFDCF\uFDF0-\uFF07\uFF10-\uFFEF]{1,256}|[\u0600-\u06FF\/]{1,256}(\s*?[\u0600-\u06FF]{1,256}){1,2}/i;function p(e,t,s){He[e]=te(t)?t:function(e,a){return e&&s?s:t}}function Se(e,a){return l(He,e)?He[e](a._strict,a._locale):new RegExp(k(e.replace("\\","").replace(/\\(\[)|\\(\])|\[([^\]\[]*)\]|\\(.)/g,function(e,a,t,s,n){return a||t||s||n})))}function k(e){return e.replace(/[-\/\\^$*+?.()|[\]{}]/g,"\\$&")}var He={},ve={};function D(e,t){var a,s,n=t;for("string"==typeof e&&(e=[e]),J(t)&&(n=function(e,a){a[t]=m(e)}),s=e.length,a=0;a<s;a++)ve[e[a]]=n}function je(e,n){D(e,function(e,a,t,s){t._w=t._w||{},n(e,t._w,t,s)})}var T,g=0,xe=1,Pe=2,w=3,Oe=4,We=5,Ae=6,Ee=7,Fe=8;function ze(e,a){if(isNaN(e)||isNaN(a))return NaN;var t=(a%(t=12)+t)%t;return e+=(a-t)/12,1==t?Le(e)?29:28:31-t%7%2}T=Array.prototype.indexOf||function(e){for(var a=0;a<this.length;++a)if(this[a]===e)return a;return-1},s("M",["MM",2],"Mo",function(){return this.month()+1}),s("MMM",0,0,function(e){return this.localeData().monthsShort(this,e)}),s("MMMM",0,0,function(e){return this.localeData().months(this,e)}),a("month","M"),t("month",8),p("M",o),p("MM",o,r),p("MMM",function(e,a){return a.monthsShortRegex(e)}),p("MMMM",function(e,a){return a.monthsRegex(e)}),D(["M","MM"],function(e,a){a[xe]=m(e)-1}),D(["MMM","MMMM"],function(e,a,t,s){s=t._locale.monthsParse(e,s,t._strict);null!=s?a[xe]=s:h(t).invalidMonth=e});var Ne="January_February_March_April_May_June_July_August_September_October_November_December".split("_"),Je="Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),Re=/D[oD]?(\[[^\[\]]*\]|\s)+MMMM?/,Ce=f,Ie=f;function Ue(e,a){var t;if(e.isValid()){if("string"==typeof a)if(/^\d+$/.test(a))a=m(a);else if(!J(a=e.localeData().monthsParse(a)))return;t=Math.min(e.date(),ze(e.year(),a)),e._d["set"+(e._isUTC?"UTC":"")+"Month"](a,t)}}function Ge(e){return null!=e?(Ue(this,e),M.updateOffset(this,!0),this):ye(this,"Month")}function Ve(){function e(e,a){return a.length-e.length}for(var a,t=[],s=[],n=[],r=0;r<12;r++)a=U([2e3,r]),t.push(this.monthsShort(a,"")),s.push(this.months(a,"")),n.push(this.months(a,"")),n.push(this.monthsShort(a,""));for(t.sort(e),s.sort(e),n.sort(e),r=0;r<12;r++)t[r]=k(t[r]),s[r]=k(s[r]);for(r=0;r<24;r++)n[r]=k(n[r]);this._monthsRegex=new RegExp("^("+n.join("|")+")","i"),this._monthsShortRegex=this._monthsRegex,this._monthsStrictRegex=new RegExp("^("+s.join("|")+")","i"),this._monthsShortStrictRegex=new RegExp("^("+t.join("|")+")","i")}function Be(e){return Le(e)?366:365}s("Y",0,0,function(){var e=this.year();return e<=9999?de(e,4):"+"+e}),s(0,["YY",2],0,function(){return this.year()%100}),s(0,["YYYY",4],0,"year"),s(0,["YYYYY",5],0,"year"),s(0,["YYYYYY",6,!0],0,"year"),a("year","y"),t("year",1),p("Y",ge),p("YY",o,r),p("YYYY",Y,ke),p("YYYYY",y,_),p("YYYYYY",y,_),D(["YYYYY","YYYYYY"],g),D("YYYY",function(e,a){a[g]=2===e.length?M.parseTwoDigitYear(e):m(e)}),D("YY",function(e,a){a[g]=M.parseTwoDigitYear(e)}),D("Y",function(e,a){a[g]=parseInt(e,10)}),M.parseTwoDigitYear=function(e){return m(e)+(68<m(e)?1900:2e3)};var Ke=Ye("FullYear",!0);function qe(e,a,t,s,n,r,d){var i;return e<100&&0<=e?(i=new Date(e+400,a,t,s,n,r,d),isFinite(i.getFullYear())&&i.setFullYear(e)):i=new Date(e,a,t,s,n,r,d),i}function Ze(e){var a;return e<100&&0<=e?((a=Array.prototype.slice.call(arguments))[0]=e+400,a=new Date(Date.UTC.apply(null,a)),isFinite(a.getUTCFullYear())&&a.setUTCFullYear(e)):a=new Date(Date.UTC.apply(null,arguments)),a}function $e(e,a,t){t=7+a-t;return t-(7+Ze(e,0,t).getUTCDay()-a)%7-1}function Qe(e,a,t,s,n){var r,a=1+7*(a-1)+(7+t-s)%7+$e(e,s,n),t=a<=0?Be(r=e-1)+a:a>Be(e)?(r=e+1,a-Be(e)):(r=e,a);return{year:r,dayOfYear:t}}function Xe(e,a,t){var s,n,r=$e(e.year(),a,t),r=Math.floor((e.dayOfYear()-r-1)/7)+1;return r<1?s=r+ea(n=e.year()-1,a,t):r>ea(e.year(),a,t)?(s=r-ea(e.year(),a,t),n=e.year()+1):(n=e.year(),s=r),{week:s,year:n}}function ea(e,a,t){var s=$e(e,a,t),a=$e(e+1,a,t);return(Be(e)-s+a)/7}s("w",["ww",2],"wo","week"),s("W",["WW",2],"Wo","isoWeek"),a("week","w"),a("isoWeek","W"),t("week",5),t("isoWeek",5),p("w",o),p("ww",o,r),p("W",o),p("WW",o,r),je(["w","ww","W","WW"],function(e,a,t,s){a[s.substr(0,1)]=m(e)});function aa(e,a){return e.slice(a,7).concat(e.slice(0,a))}s("d",0,"do","day"),s("dd",0,0,function(e){return this.localeData().weekdaysMin(this,e)}),s("ddd",0,0,function(e){return this.localeData().weekdaysShort(this,e)}),s("dddd",0,0,function(e){return this.localeData().weekdays(this,e)}),s("e",0,0,"weekday"),s("E",0,0,"isoWeekday"),a("day","d"),a("weekday","e"),a("isoWeekday","E"),t("day",11),t("weekday",11),t("isoWeekday",11),p("d",o),p("e",o),p("E",o),p("dd",function(e,a){return a.weekdaysMinRegex(e)}),p("ddd",function(e,a){return a.weekdaysShortRegex(e)}),p("dddd",function(e,a){return a.weekdaysRegex(e)}),je(["dd","ddd","dddd"],function(e,a,t,s){s=t._locale.weekdaysParse(e,s,t._strict);null!=s?a.d=s:h(t).invalidWeekday=e}),je(["d","e","E"],function(e,a,t,s){a[s]=m(e)});var ta="Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),sa="Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),na="Su_Mo_Tu_We_Th_Fr_Sa".split("_"),ra=f,da=f,ia=f;function _a(){function e(e,a){return a.length-e.length}for(var a,t,s,n=[],r=[],d=[],i=[],_=0;_<7;_++)s=U([2e3,1]).day(_),a=k(this.weekdaysMin(s,"")),t=k(this.weekdaysShort(s,"")),s=k(this.weekdays(s,"")),n.push(a),r.push(t),d.push(s),i.push(a),i.push(t),i.push(s);n.sort(e),r.sort(e),d.sort(e),i.sort(e),this._weekdaysRegex=new RegExp("^("+i.join("|")+")","i"),this._weekdaysShortRegex=this._weekdaysRegex,this._weekdaysMinRegex=this._weekdaysRegex,this._weekdaysStrictRegex=new RegExp("^("+d.join("|")+")","i"),this._weekdaysShortStrictRegex=new RegExp("^("+r.join("|")+")","i"),this._weekdaysMinStrictRegex=new RegExp("^("+n.join("|")+")","i")}function oa(){return this.hours()%12||12}function ma(e,a){s(e,0,0,function(){return this.localeData().meridiem(this.hours(),this.minutes(),a)})}function ua(e,a){return a._meridiemParse}s("H",["HH",2],0,"hour"),s("h",["hh",2],0,oa),s("k",["kk",2],0,function(){return this.hours()||24}),s("hmm",0,0,function(){return""+oa.apply(this)+de(this.minutes(),2)}),s("hmmss",0,0,function(){return""+oa.apply(this)+de(this.minutes(),2)+de(this.seconds(),2)}),s("Hmm",0,0,function(){return""+this.hours()+de(this.minutes(),2)}),s("Hmmss",0,0,function(){return""+this.hours()+de(this.minutes(),2)+de(this.seconds(),2)}),ma("a",!0),ma("A",!1),a("hour","h"),t("hour",13),p("a",ua),p("A",ua),p("H",o),p("h",o),p("k",o),p("HH",o,r),p("hh",o,r),p("kk",o,r),p("hmm",c),p("hmmss",L),p("Hmm",c),p("Hmmss",L),D(["H","HH"],w),D(["k","kk"],function(e,a,t){e=m(e);a[w]=24===e?0:e}),D(["a","A"],function(e,a,t){t._isPm=t._locale.isPM(e),t._meridiem=e}),D(["h","hh"],function(e,a,t){a[w]=m(e),h(t).bigHour=!0}),D("hmm",function(e,a,t){var s=e.length-2;a[w]=m(e.substr(0,s)),a[Oe]=m(e.substr(s)),h(t).bigHour=!0}),D("hmmss",function(e,a,t){var s=e.length-4,n=e.length-2;a[w]=m(e.substr(0,s)),a[Oe]=m(e.substr(s,2)),a[We]=m(e.substr(n)),h(t).bigHour=!0}),D("Hmm",function(e,a,t){var s=e.length-2;a[w]=m(e.substr(0,s)),a[Oe]=m(e.substr(s))}),D("Hmmss",function(e,a,t){var s=e.length-4,n=e.length-2;a[w]=m(e.substr(0,s)),a[Oe]=m(e.substr(s,2)),a[We]=m(e.substr(n))});f=Ye("Hours",!0);var la,Ma={calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},longDateFormat:{LTS:"h:mm:ss A",LT:"h:mm A",L:"MM/DD/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY h:mm A",LLLL:"dddd, MMMM D, YYYY h:mm A"},invalidDate:"Invalid date",ordinal:"%d",dayOfMonthOrdinalParse:/\d{1,2}/,relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",w:"a week",ww:"%d weeks",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},months:Ne,monthsShort:Je,week:{dow:0,doy:6},weekdays:ta,weekdaysMin:na,weekdaysShort:sa,meridiemParse:/[ap]\.?m?\.?/i},b={},ha={};function ca(e){return e&&e.toLowerCase().replace("_","-")}function La(e){for(var a,t,s,n,r=0;r<e.length;){for(a=(n=ca(e[r]).split("-")).length,t=(t=ca(e[r+1]))?t.split("-"):null;0<a;){if(s=Ya(n.slice(0,a).join("-")))return s;if(t&&t.length>=a&&function(e,a){for(var t=Math.min(e.length,a.length),s=0;s<t;s+=1)if(e[s]!==a[s])return s;return t}(n,t)>=a-1)break;a--}r++}return la}function Ya(a){var e;if(void 0===b[a]&&"undefined"!=typeof module&&module&&module.exports&&null!=a.match("^[^/\\\\]*$"))try{e=la._abbr,require("./locale/"+a),ya(e)}catch(e){b[a]=null}return b[a]}function ya(e,a){return e&&((a=d(a)?pa(e):fa(e,a))?la=a:"undefined"!=typeof console&&console.warn&&console.warn("Locale "+e+" not found. Did you forget to load it?")),la._abbr}function fa(e,a){if(null===a)return delete b[e],null;var t,s=Ma;if(a.abbr=e,null!=b[e])ae("defineLocaleOverride","use moment.updateLocale(localeName, config) to change an existing locale. moment.defineLocale(localeName, config) should only be used for creating a new locale See http://momentjs.com/guides/#/warnings/define-locale/ for more info."),s=b[e]._config;else if(null!=a.parentLocale)if(null!=b[a.parentLocale])s=b[a.parentLocale]._config;else{if(null==(t=Ya(a.parentLocale)))return ha[a.parentLocale]||(ha[a.parentLocale]=[]),ha[a.parentLocale].push({name:e,config:a}),null;s=t._config}return b[e]=new ne(se(s,a)),ha[e]&&ha[e].forEach(function(e){fa(e.name,e.config)}),ya(e),b[e]}function pa(e){var a;if(!(e=e&&e._locale&&e._locale._abbr?e._locale._abbr:e))return la;if(!F(e)){if(a=Ya(e))return a;e=[e]}return La(e)}function ka(e){var a=e._a;return a&&-2===h(e).overflow&&(a=a[xe]<0||11<a[xe]?xe:a[Pe]<1||a[Pe]>ze(a[g],a[xe])?Pe:a[w]<0||24<a[w]||24===a[w]&&(0!==a[Oe]||0!==a[We]||0!==a[Ae])?w:a[Oe]<0||59<a[Oe]?Oe:a[We]<0||59<a[We]?We:a[Ae]<0||999<a[Ae]?Ae:-1,h(e)._overflowDayOfYear&&(a<g||Pe<a)&&(a=Pe),h(e)._overflowWeeks&&-1===a&&(a=Ee),h(e)._overflowWeekday&&-1===a&&(a=Fe),h(e).overflow=a),e}var Da=/^\s*((?:[+-]\d{6}|\d{4})-(?:\d\d-\d\d|W\d\d-\d|W\d\d|\d\d\d|\d\d))(?:(T| )(\d\d(?::\d\d(?::\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,Ta=/^\s*((?:[+-]\d{6}|\d{4})(?:\d\d\d\d|W\d\d\d|W\d\d|\d\d\d|\d\d|))(?:(T| )(\d\d(?:\d\d(?:\d\d(?:[.,]\d+)?)?)?)([+-]\d\d(?::?\d\d)?|\s*Z)?)?$/,ga=/Z|[+-]\d\d(?::?\d\d)?/,wa=[["YYYYYY-MM-DD",/[+-]\d{6}-\d\d-\d\d/],["YYYY-MM-DD",/\d{4}-\d\d-\d\d/],["GGGG-[W]WW-E",/\d{4}-W\d\d-\d/],["GGGG-[W]WW",/\d{4}-W\d\d/,!1],["YYYY-DDD",/\d{4}-\d{3}/],["YYYY-MM",/\d{4}-\d\d/,!1],["YYYYYYMMDD",/[+-]\d{10}/],["YYYYMMDD",/\d{8}/],["GGGG[W]WWE",/\d{4}W\d{3}/],["GGGG[W]WW",/\d{4}W\d{2}/,!1],["YYYYDDD",/\d{7}/],["YYYYMM",/\d{6}/,!1],["YYYY",/\d{4}/,!1]],ba=[["HH:mm:ss.SSSS",/\d\d:\d\d:\d\d\.\d+/],["HH:mm:ss,SSSS",/\d\d:\d\d:\d\d,\d+/],["HH:mm:ss",/\d\d:\d\d:\d\d/],["HH:mm",/\d\d:\d\d/],["HHmmss.SSSS",/\d\d\d\d\d\d\.\d+/],["HHmmss,SSSS",/\d\d\d\d\d\d,\d+/],["HHmmss",/\d\d\d\d\d\d/],["HHmm",/\d\d\d\d/],["HH",/\d\d/]],Sa=/^\/?Date\((-?\d+)/i,Ha=/^(?:(Mon|Tue|Wed|Thu|Fri|Sat|Sun),?\s)?(\d{1,2})\s(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s(\d{2,4})\s(\d\d):(\d\d)(?::(\d\d))?\s(?:(UT|GMT|[ECMP][SD]T)|([Zz])|([+-]\d{4}))$/,va={UT:0,GMT:0,EDT:-240,EST:-300,CDT:-300,CST:-360,MDT:-360,MST:-420,PDT:-420,PST:-480};function ja(e){var a,t,s,n,r,d,i=e._i,_=Da.exec(i)||Ta.exec(i),i=wa.length,o=ba.length;if(_){for(h(e).iso=!0,a=0,t=i;a<t;a++)if(wa[a][1].exec(_[1])){n=wa[a][0],s=!1!==wa[a][2];break}if(null==n)e._isValid=!1;else{if(_[3]){for(a=0,t=o;a<t;a++)if(ba[a][1].exec(_[3])){r=(_[2]||" ")+ba[a][0];break}if(null==r)return void(e._isValid=!1)}if(s||null==r){if(_[4]){if(!ga.exec(_[4]))return void(e._isValid=!1);d="Z"}e._f=n+(r||"")+(d||""),Aa(e)}else e._isValid=!1}}else e._isValid=!1}function xa(e,a,t,s,n,r){e=[function(e){e=parseInt(e,10);{if(e<=49)return 2e3+e;if(e<=999)return 1900+e}return e}(e),Je.indexOf(a),parseInt(t,10),parseInt(s,10),parseInt(n,10)];return r&&e.push(parseInt(r,10)),e}function Pa(e){var a,t,s,n,r=Ha.exec(e._i.replace(/\([^)]*\)|[\n\t]/g," ").replace(/(\s\s+)/g," ").replace(/^\s\s*/,"").replace(/\s\s*$/,""));r?(a=xa(r[4],r[3],r[2],r[5],r[6],r[7]),t=r[1],s=a,n=e,t&&sa.indexOf(t)!==new Date(s[0],s[1],s[2]).getDay()?(h(n).weekdayMismatch=!0,n._isValid=!1):(e._a=a,e._tzm=(t=r[8],s=r[9],n=r[10],t?va[t]:s?0:60*(((t=parseInt(n,10))-(s=t%100))/100)+s),e._d=Ze.apply(null,e._a),e._d.setUTCMinutes(e._d.getUTCMinutes()-e._tzm),h(e).rfc2822=!0)):e._isValid=!1}function Oa(e,a,t){return null!=e?e:null!=a?a:t}function Wa(e){var a,t,s,n,r,d,i,_,o,m,u,l=[];if(!e._d){for(s=e,n=new Date(M.now()),t=s._useUTC?[n.getUTCFullYear(),n.getUTCMonth(),n.getUTCDate()]:[n.getFullYear(),n.getMonth(),n.getDate()],e._w&&null==e._a[Pe]&&null==e._a[xe]&&(null!=(n=(s=e)._w).GG||null!=n.W||null!=n.E?(_=1,o=4,r=Oa(n.GG,s._a[g],Xe(S(),1,4).year),d=Oa(n.W,1),((i=Oa(n.E,1))<1||7<i)&&(m=!0)):(_=s._locale._week.dow,o=s._locale._week.doy,u=Xe(S(),_,o),r=Oa(n.gg,s._a[g],u.year),d=Oa(n.w,u.week),null!=n.d?((i=n.d)<0||6<i)&&(m=!0):null!=n.e?(i=n.e+_,(n.e<0||6<n.e)&&(m=!0)):i=_),d<1||d>ea(r,_,o)?h(s)._overflowWeeks=!0:null!=m?h(s)._overflowWeekday=!0:(u=Qe(r,d,i,_,o),s._a[g]=u.year,s._dayOfYear=u.dayOfYear)),null!=e._dayOfYear&&(n=Oa(e._a[g],t[g]),(e._dayOfYear>Be(n)||0===e._dayOfYear)&&(h(e)._overflowDayOfYear=!0),m=Ze(n,0,e._dayOfYear),e._a[xe]=m.getUTCMonth(),e._a[Pe]=m.getUTCDate()),a=0;a<3&&null==e._a[a];++a)e._a[a]=l[a]=t[a];for(;a<7;a++)e._a[a]=l[a]=null==e._a[a]?2===a?1:0:e._a[a];24===e._a[w]&&0===e._a[Oe]&&0===e._a[We]&&0===e._a[Ae]&&(e._nextDay=!0,e._a[w]=0),e._d=(e._useUTC?Ze:qe).apply(null,l),r=e._useUTC?e._d.getUTCDay():e._d.getDay(),null!=e._tzm&&e._d.setUTCMinutes(e._d.getUTCMinutes()-e._tzm),e._nextDay&&(e._a[w]=24),e._w&&void 0!==e._w.d&&e._w.d!==r&&(h(e).weekdayMismatch=!0)}}function Aa(e){if(e._f===M.ISO_8601)ja(e);else if(e._f===M.RFC_2822)Pa(e);else{e._a=[],h(e).empty=!0;for(var a,t,s,n,r,d=""+e._i,i=d.length,_=0,o=le(e._f,e._locale).match(ie)||[],m=o.length,u=0;u<m;u++)t=o[u],(a=(d.match(Se(t,e))||[])[0])&&(0<(s=d.substr(0,d.indexOf(a))).length&&h(e).unusedInput.push(s),d=d.slice(d.indexOf(a)+a.length),_+=a.length),me[t]?(a?h(e).empty=!1:h(e).unusedTokens.push(t),s=t,r=e,null!=(n=a)&&l(ve,s)&&ve[s](n,r._a,r,s)):e._strict&&!a&&h(e).unusedTokens.push(t);h(e).charsLeftOver=i-_,0<d.length&&h(e).unusedInput.push(d),e._a[w]<=12&&!0===h(e).bigHour&&0<e._a[w]&&(h(e).bigHour=void 0),h(e).parsedDateParts=e._a.slice(0),h(e).meridiem=e._meridiem,e._a[w]=function(e,a,t){if(null==t)return a;return null!=e.meridiemHour?e.meridiemHour(a,t):null!=e.isPM?((e=e.isPM(t))&&a<12&&(a+=12),a=e||12!==a?a:0):a}(e._locale,e._a[w],e._meridiem),null!==(i=h(e).era)&&(e._a[g]=e._locale.erasConvertYear(i,e._a[g])),Wa(e),ka(e)}}function Ea(e){var a,t,s,n=e._i,r=e._f;if(e._locale=e._locale||pa(e._l),null===n||void 0===r&&""===n)return V({nullInput:!0});if("string"==typeof n&&(e._i=n=e._locale.preparse(n)),Q(n))return new $(ka(n));if(R(n))e._d=n;else if(F(r))!function(e){var a,t,s,n,r,d,i=!1,_=e._f.length;if(0===_)return h(e).invalidFormat=!0,e._d=new Date(NaN);for(n=0;n<_;n++)r=0,d=!1,a=Z({},e),null!=e._useUTC&&(a._useUTC=e._useUTC),a._f=e._f[n],Aa(a),G(a)&&(d=!0),r=(r+=h(a).charsLeftOver)+10*h(a).unusedTokens.length,h(a).score=r,i?r<s&&(s=r,t=a):(null==s||r<s||d)&&(s=r,t=a,d&&(i=!0));I(e,t||a)}(e);else if(r)Aa(e);else if(d(r=(n=e)._i))n._d=new Date(M.now());else R(r)?n._d=new Date(r.valueOf()):"string"==typeof r?(t=n,null!==(a=Sa.exec(t._i))?t._d=new Date(+a[1]):(ja(t),!1===t._isValid&&(delete t._isValid,Pa(t),!1===t._isValid&&(delete t._isValid,t._strict?t._isValid=!1:M.createFromInputFallback(t))))):F(r)?(n._a=C(r.slice(0),function(e){return parseInt(e,10)}),Wa(n)):z(r)?(a=n)._d||(s=void 0===(t=he(a._i)).day?t.date:t.day,a._a=C([t.year,t.month,s,t.hour,t.minute,t.second,t.millisecond],function(e){return e&&parseInt(e,10)}),Wa(a)):J(r)?n._d=new Date(r):M.createFromInputFallback(n);return G(e)||(e._d=null),e}function Fa(e,a,t,s,n){var r={};return!0!==a&&!1!==a||(s=a,a=void 0),!0!==t&&!1!==t||(s=t,t=void 0),(z(e)&&N(e)||F(e)&&0===e.length)&&(e=void 0),r._isAMomentObject=!0,r._useUTC=r._isUTC=n,r._l=t,r._i=e,r._f=a,r._strict=s,(n=new $(ka(Ea(n=r))))._nextDay&&(n.add(1,"d"),n._nextDay=void 0),n}function S(e,a,t,s){return Fa(e,a,t,s,!1)}M.createFromInputFallback=e("value provided is not in a recognized RFC2822 or ISO format. moment construction falls back to js Date(), which is not reliable across all browsers and versions. Non RFC2822/ISO date formats are discouraged. Please refer to http://momentjs.com/guides/#/warnings/js-date/ for more info.",function(e){e._d=new Date(e._i+(e._useUTC?" UTC":""))}),M.ISO_8601=function(){},M.RFC_2822=function(){};c=e("moment().min is deprecated, use moment.max instead. http://momentjs.com/guides/#/warnings/min-max/",function(){var e=S.apply(null,arguments);return this.isValid()&&e.isValid()?e<this?this:e:V()}),L=e("moment().max is deprecated, use moment.min instead. http://momentjs.com/guides/#/warnings/min-max/",function(){var e=S.apply(null,arguments);return this.isValid()&&e.isValid()?this<e?this:e:V()});function za(e,a){var t,s;if(!(a=1===a.length&&F(a[0])?a[0]:a).length)return S();for(t=a[0],s=1;s<a.length;++s)a[s].isValid()&&!a[s][e](t)||(t=a[s]);return t}var Na=["year","quarter","month","week","day","hour","minute","second","millisecond"];function Ja(e){var e=he(e),a=e.year||0,t=e.quarter||0,s=e.month||0,n=e.week||e.isoWeek||0,r=e.day||0,d=e.hour||0,i=e.minute||0,_=e.second||0,o=e.millisecond||0;this._isValid=function(e){var a,t,s=!1,n=Na.length;for(a in e)if(l(e,a)&&(-1===T.call(Na,a)||null!=e[a]&&isNaN(e[a])))return!1;for(t=0;t<n;++t)if(e[Na[t]]){if(s)return!1;parseFloat(e[Na[t]])!==m(e[Na[t]])&&(s=!0)}return!0}(e),this._milliseconds=+o+1e3*_+6e4*i+1e3*d*60*60,this._days=+r+7*n,this._months=+s+3*t+12*a,this._data={},this._locale=pa(),this._bubble()}function Ra(e){return e instanceof Ja}function Ca(e){return e<0?-1*Math.round(-1*e):Math.round(e)}function Ia(e,t){s(e,0,0,function(){var e=this.utcOffset(),a="+";return e<0&&(e=-e,a="-"),a+de(~~(e/60),2)+t+de(~~e%60,2)})}Ia("Z",":"),Ia("ZZ",""),p("Z",be),p("ZZ",be),D(["Z","ZZ"],function(e,a,t){t._useUTC=!0,t._tzm=Ga(be,e)});var Ua=/([\+\-]|\d\d)/gi;function Ga(e,a){var a=(a||"").match(e);return null===a?null:0===(a=60*(e=((a[a.length-1]||[])+"").match(Ua)||["-",0,0])[1]+m(e[2]))?0:"+"===e[0]?a:-a}function Va(e,a){var t;return a._isUTC?(a=a.clone(),t=(Q(e)||R(e)?e:S(e)).valueOf()-a.valueOf(),a._d.setTime(a._d.valueOf()+t),M.updateOffset(a,!1),a):S(e).local()}function Ba(e){return-Math.round(e._d.getTimezoneOffset())}function Ka(){return!!this.isValid()&&(this._isUTC&&0===this._offset)}M.updateOffset=function(){};var qa=/^(-|\+)?(?:(\d*)[. ])?(\d+):(\d+)(?::(\d+)(\.\d*)?)?$/,Za=/^(-|\+)?P(?:([-+]?[0-9,.]*)Y)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)W)?(?:([-+]?[0-9,.]*)D)?(?:T(?:([-+]?[0-9,.]*)H)?(?:([-+]?[0-9,.]*)M)?(?:([-+]?[0-9,.]*)S)?)?$/;function $a(e,a){var t,s=e,n=null;return Ra(e)?s={ms:e._milliseconds,d:e._days,M:e._months}:J(e)||!isNaN(+e)?(s={},a?s[a]=+e:s.milliseconds=+e):(n=qa.exec(e))?(t="-"===n[1]?-1:1,s={y:0,d:m(n[Pe])*t,h:m(n[w])*t,m:m(n[Oe])*t,s:m(n[We])*t,ms:m(Ca(1e3*n[Ae]))*t}):(n=Za.exec(e))?(t="-"===n[1]?-1:1,s={y:Qa(n[2],t),M:Qa(n[3],t),w:Qa(n[4],t),d:Qa(n[5],t),h:Qa(n[6],t),m:Qa(n[7],t),s:Qa(n[8],t)}):null==s?s={}:"object"==typeof s&&("from"in s||"to"in s)&&(a=function(e,a){var t;if(!e.isValid()||!a.isValid())return{milliseconds:0,months:0};a=Va(a,e),e.isBefore(a)?t=Xa(e,a):((t=Xa(a,e)).milliseconds=-t.milliseconds,t.months=-t.months);return t}(S(s.from),S(s.to)),(s={}).ms=a.milliseconds,s.M=a.months),n=new Ja(s),Ra(e)&&l(e,"_locale")&&(n._locale=e._locale),Ra(e)&&l(e,"_isValid")&&(n._isValid=e._isValid),n}function Qa(e,a){e=e&&parseFloat(e.replace(",","."));return(isNaN(e)?0:e)*a}function Xa(e,a){var t={};return t.months=a.month()-e.month()+12*(a.year()-e.year()),e.clone().add(t.months,"M").isAfter(a)&&--t.months,t.milliseconds=+a-+e.clone().add(t.months,"M"),t}function et(s,n){return function(e,a){var t;return null===a||isNaN(+a)||(ae(n,"moment()."+n+"(period, number) is deprecated. Please use moment()."+n+"(number, period). See http://momentjs.com/guides/#/warnings/add-inverted-param/ for more info."),t=e,e=a,a=t),at(this,$a(e,a),s),this}}function at(e,a,t,s){var n=a._milliseconds,r=Ca(a._days),a=Ca(a._months);e.isValid()&&(s=null==s||s,a&&Ue(e,ye(e,"Month")+a*t),r&&fe(e,"Date",ye(e,"Date")+r*t),n&&e._d.setTime(e._d.valueOf()+n*t),s&&M.updateOffset(e,r||a))}$a.fn=Ja.prototype,$a.invalid=function(){return $a(NaN)};Ne=et(1,"add"),ta=et(-1,"subtract");function tt(e){return"string"==typeof e||e instanceof String}function st(e){return Q(e)||R(e)||tt(e)||J(e)||function(a){var e=F(a),t=!1;e&&(t=0===a.filter(function(e){return!J(e)&&tt(a)}).length);return e&&t}(e)||function(e){var a,t,s=z(e)&&!N(e),n=!1,r=["years","year","y","months","month","M","days","day","d","dates","date","D","hours","hour","h","minutes","minute","m","seconds","second","s","milliseconds","millisecond","ms"],d=r.length;for(a=0;a<d;a+=1)t=r[a],n=n||l(e,t);return s&&n}(e)||null==e}function nt(e,a){if(e.date()<a.date())return-nt(a,e);var t=12*(a.year()-e.year())+(a.month()-e.month()),s=e.clone().add(t,"months"),a=a-s<0?(a-s)/(s-e.clone().add(t-1,"months")):(a-s)/(e.clone().add(1+t,"months")-s);return-(t+a)||0}function rt(e){return void 0===e?this._locale._abbr:(null!=(e=pa(e))&&(this._locale=e),this)}M.defaultFormat="YYYY-MM-DDTHH:mm:ssZ",M.defaultFormatUtc="YYYY-MM-DDTHH:mm:ss[Z]";na=e("moment().lang() is deprecated. Instead, use moment().localeData() to get the language configuration. Use moment().locale() to change languages.",function(e){return void 0===e?this.localeData():this.locale(e)});function dt(){return this._locale}var it=126227808e5;function _t(e,a){return(e%a+a)%a}function ot(e,a,t){return e<100&&0<=e?new Date(e+400,a,t)-it:new Date(e,a,t).valueOf()}function mt(e,a,t){return e<100&&0<=e?Date.UTC(e+400,a,t)-it:Date.UTC(e,a,t)}function ut(e,a){return a.erasAbbrRegex(e)}function lt(){for(var e=[],a=[],t=[],s=[],n=this.eras(),r=0,d=n.length;r<d;++r)a.push(k(n[r].name)),e.push(k(n[r].abbr)),t.push(k(n[r].narrow)),s.push(k(n[r].name)),s.push(k(n[r].abbr)),s.push(k(n[r].narrow));this._erasRegex=new RegExp("^("+s.join("|")+")","i"),this._erasNameRegex=new RegExp("^("+a.join("|")+")","i"),this._erasAbbrRegex=new RegExp("^("+e.join("|")+")","i"),this._erasNarrowRegex=new RegExp("^("+t.join("|")+")","i")}function Mt(e,a){s(0,[e,e.length],0,a)}function ht(e,a,t,s,n){var r;return null==e?Xe(this,s,n).year:(r=ea(e,s,n),function(e,a,t,s,n){e=Qe(e,a,t,s,n),a=Ze(e.year,0,e.dayOfYear);return this.year(a.getUTCFullYear()),this.month(a.getUTCMonth()),this.date(a.getUTCDate()),this}.call(this,e,a=r<a?r:a,t,s,n))}s("N",0,0,"eraAbbr"),s("NN",0,0,"eraAbbr"),s("NNN",0,0,"eraAbbr"),s("NNNN",0,0,"eraName"),s("NNNNN",0,0,"eraNarrow"),s("y",["y",1],"yo","eraYear"),s("y",["yy",2],0,"eraYear"),s("y",["yyy",3],0,"eraYear"),s("y",["yyyy",4],0,"eraYear"),p("N",ut),p("NN",ut),p("NNN",ut),p("NNNN",function(e,a){return a.erasNameRegex(e)}),p("NNNNN",function(e,a){return a.erasNarrowRegex(e)}),D(["N","NN","NNN","NNNN","NNNNN"],function(e,a,t,s){s=t._locale.erasParse(e,s,t._strict);s?h(t).era=s:h(t).invalidEra=e}),p("y",Te),p("yy",Te),p("yyy",Te),p("yyyy",Te),p("yo",function(e,a){return a._eraYearOrdinalRegex||Te}),D(["y","yy","yyy","yyyy"],g),D(["yo"],function(e,a,t,s){var n;t._locale._eraYearOrdinalRegex&&(n=e.match(t._locale._eraYearOrdinalRegex)),t._locale.eraYearOrdinalParse?a[g]=t._locale.eraYearOrdinalParse(e,n):a[g]=parseInt(e,10)}),s(0,["gg",2],0,function(){return this.weekYear()%100}),s(0,["GG",2],0,function(){return this.isoWeekYear()%100}),Mt("gggg","weekYear"),Mt("ggggg","weekYear"),Mt("GGGG","isoWeekYear"),Mt("GGGGG","isoWeekYear"),a("weekYear","gg"),a("isoWeekYear","GG"),t("weekYear",1),t("isoWeekYear",1),p("G",ge),p("g",ge),p("GG",o,r),p("gg",o,r),p("GGGG",Y,ke),p("gggg",Y,ke),p("GGGGG",y,_),p("ggggg",y,_),je(["gggg","ggggg","GGGG","GGGGG"],function(e,a,t,s){a[s.substr(0,2)]=m(e)}),je(["gg","GG"],function(e,a,t,s){a[s]=M.parseTwoDigitYear(e)}),s("Q",0,"Qo","quarter"),a("quarter","Q"),t("quarter",7),p("Q",n),D("Q",function(e,a){a[xe]=3*(m(e)-1)}),s("D",["DD",2],"Do","date"),a("date","D"),t("date",9),p("D",o),p("DD",o,r),p("Do",function(e,a){return e?a._dayOfMonthOrdinalParse||a._ordinalParse:a._dayOfMonthOrdinalParseLenient}),D(["D","DD"],Pe),D("Do",function(e,a){a[Pe]=m(e.match(o)[0])});Y=Ye("Date",!0);s("DDD",["DDDD",3],"DDDo","dayOfYear"),a("dayOfYear","DDD"),t("dayOfYear",4),p("DDD",De),p("DDDD",pe),D(["DDD","DDDD"],function(e,a,t){t._dayOfYear=m(e)}),s("m",["mm",2],0,"minute"),a("minute","m"),t("minute",14),p("m",o),p("mm",o,r),D(["m","mm"],Oe);var ct,ke=Ye("Minutes",!1),y=(s("s",["ss",2],0,"second"),a("second","s"),t("second",15),p("s",o),p("ss",o,r),D(["s","ss"],We),Ye("Seconds",!1));for(s("S",0,0,function(){return~~(this.millisecond()/100)}),s(0,["SS",2],0,function(){return~~(this.millisecond()/10)}),s(0,["SSS",3],0,"millisecond"),s(0,["SSSS",4],0,function(){return 10*this.millisecond()}),s(0,["SSSSS",5],0,function(){return 100*this.millisecond()}),s(0,["SSSSSS",6],0,function(){return 1e3*this.millisecond()}),s(0,["SSSSSSS",7],0,function(){return 1e4*this.millisecond()}),s(0,["SSSSSSSS",8],0,function(){return 1e5*this.millisecond()}),s(0,["SSSSSSSSS",9],0,function(){return 1e6*this.millisecond()}),a("millisecond","ms"),t("millisecond",16),p("S",De,n),p("SS",De,r),p("SSS",De,pe),ct="SSSS";ct.length<=9;ct+="S")p(ct,Te);function Lt(e,a){a[Ae]=m(1e3*("0."+e))}for(ct="S";ct.length<=9;ct+="S")D(ct,Lt);_=Ye("Milliseconds",!1),s("z",0,0,"zoneAbbr"),s("zz",0,0,"zoneName");n=$.prototype;function Yt(e){return e}n.add=Ne,n.calendar=function(e,a){1===arguments.length&&(arguments[0]?st(arguments[0])?(e=arguments[0],a=void 0):function(e){for(var a=z(e)&&!N(e),t=!1,s=["sameDay","nextDay","lastDay","nextWeek","lastWeek","sameElse"],n=0;n<s.length;n+=1)t=t||l(e,s[n]);return a&&t}(arguments[0])&&(a=arguments[0],e=void 0):a=e=void 0);var e=e||S(),t=Va(e,this).startOf("day"),t=M.calendarFormat(this,t)||"sameElse",a=a&&(te(a[t])?a[t].call(this,e):a[t]);return this.format(a||this.localeData().calendar(t,this,S(e)))},n.clone=function(){return new $(this)},n.diff=function(e,a,t){var s,n,r;if(!this.isValid())return NaN;if(!(s=Va(e,this)).isValid())return NaN;switch(n=6e4*(s.utcOffset()-this.utcOffset()),a=i(a)){case"year":r=nt(this,s)/12;break;case"month":r=nt(this,s);break;case"quarter":r=nt(this,s)/3;break;case"second":r=(this-s)/1e3;break;case"minute":r=(this-s)/6e4;break;case"hour":r=(this-s)/36e5;break;case"day":r=(this-s-n)/864e5;break;case"week":r=(this-s-n)/6048e5;break;default:r=this-s}return t?r:u(r)},n.endOf=function(e){var a,t;if(void 0===(e=i(e))||"millisecond"===e||!this.isValid())return this;switch(t=this._isUTC?mt:ot,e){case"year":a=t(this.year()+1,0,1)-1;break;case"quarter":a=t(this.year(),this.month()-this.month()%3+3,1)-1;break;case"month":a=t(this.year(),this.month()+1,1)-1;break;case"week":a=t(this.year(),this.month(),this.date()-this.weekday()+7)-1;break;case"isoWeek":a=t(this.year(),this.month(),this.date()-(this.isoWeekday()-1)+7)-1;break;case"day":case"date":a=t(this.year(),this.month(),this.date()+1)-1;break;case"hour":a=this._d.valueOf(),a+=36e5-_t(a+(this._isUTC?0:6e4*this.utcOffset()),36e5)-1;break;case"minute":a=this._d.valueOf(),a+=6e4-_t(a,6e4)-1;break;case"second":a=this._d.valueOf(),a+=1e3-_t(a,1e3)-1;break}return this._d.setTime(a),M.updateOffset(this,!0),this},n.format=function(e){return e=e||(this.isUtc()?M.defaultFormatUtc:M.defaultFormat),e=ue(this,e),this.localeData().postformat(e)},n.from=function(e,a){return this.isValid()&&(Q(e)&&e.isValid()||S(e).isValid())?$a({to:this,from:e}).locale(this.locale()).humanize(!a):this.localeData().invalidDate()},n.fromNow=function(e){return this.from(S(),e)},n.to=function(e,a){return this.isValid()&&(Q(e)&&e.isValid()||S(e).isValid())?$a({from:this,to:e}).locale(this.locale()).humanize(!a):this.localeData().invalidDate()},n.toNow=function(e){return this.to(S(),e)},n.get=function(e){return te(this[e=i(e)])?this[e]():this},n.invalidAt=function(){return h(this).overflow},n.isAfter=function(e,a){return e=Q(e)?e:S(e),!(!this.isValid()||!e.isValid())&&("millisecond"===(a=i(a)||"millisecond")?this.valueOf()>e.valueOf():e.valueOf()<this.clone().startOf(a).valueOf())},n.isBefore=function(e,a){return e=Q(e)?e:S(e),!(!this.isValid()||!e.isValid())&&("millisecond"===(a=i(a)||"millisecond")?this.valueOf()<e.valueOf():this.clone().endOf(a).valueOf()<e.valueOf())},n.isBetween=function(e,a,t,s){return e=Q(e)?e:S(e),a=Q(a)?a:S(a),!!(this.isValid()&&e.isValid()&&a.isValid())&&(("("===(s=s||"()")[0]?this.isAfter(e,t):!this.isBefore(e,t))&&(")"===s[1]?this.isBefore(a,t):!this.isAfter(a,t)))},n.isSame=function(e,a){var e=Q(e)?e:S(e);return!(!this.isValid()||!e.isValid())&&("millisecond"===(a=i(a)||"millisecond")?this.valueOf()===e.valueOf():(e=e.valueOf(),this.clone().startOf(a).valueOf()<=e&&e<=this.clone().endOf(a).valueOf()))},n.isSameOrAfter=function(e,a){return this.isSame(e,a)||this.isAfter(e,a)},n.isSameOrBefore=function(e,a){return this.isSame(e,a)||this.isBefore(e,a)},n.isValid=function(){return G(this)},n.lang=na,n.locale=rt,n.localeData=dt,n.max=L,n.min=c,n.parsingFlags=function(){return I({},h(this))},n.set=function(e,a){if("object"==typeof e)for(var t=function(e){var a,t=[];for(a in e)l(e,a)&&t.push({unit:a,priority:ce[a]});return t.sort(function(e,a){return e.priority-a.priority}),t}(e=he(e)),s=t.length,n=0;n<s;n++)this[t[n].unit](e[t[n].unit]);else if(te(this[e=i(e)]))return this[e](a);return this},n.startOf=function(e){var a,t;if(void 0===(e=i(e))||"millisecond"===e||!this.isValid())return this;switch(t=this._isUTC?mt:ot,e){case"year":a=t(this.year(),0,1);break;case"quarter":a=t(this.year(),this.month()-this.month()%3,1);break;case"month":a=t(this.year(),this.month(),1);break;case"week":a=t(this.year(),this.month(),this.date()-this.weekday());break;case"isoWeek":a=t(this.year(),this.month(),this.date()-(this.isoWeekday()-1));break;case"day":case"date":a=t(this.year(),this.month(),this.date());break;case"hour":a=this._d.valueOf(),a-=_t(a+(this._isUTC?0:6e4*this.utcOffset()),36e5);break;case"minute":a=this._d.valueOf(),a-=_t(a,6e4);break;case"second":a=this._d.valueOf(),a-=_t(a,1e3);break}return this._d.setTime(a),M.updateOffset(this,!0),this},n.subtract=ta,n.toArray=function(){var e=this;return[e.year(),e.month(),e.date(),e.hour(),e.minute(),e.second(),e.millisecond()]},n.toObject=function(){var e=this;return{years:e.year(),months:e.month(),date:e.date(),hours:e.hours(),minutes:e.minutes(),seconds:e.seconds(),milliseconds:e.milliseconds()}},n.toDate=function(){return new Date(this.valueOf())},n.toISOString=function(e){if(!this.isValid())return null;var a=(e=!0!==e)?this.clone().utc():this;return a.year()<0||9999<a.year()?ue(a,e?"YYYYYY-MM-DD[T]HH:mm:ss.SSS[Z]":"YYYYYY-MM-DD[T]HH:mm:ss.SSSZ"):te(Date.prototype.toISOString)?e?this.toDate().toISOString():new Date(this.valueOf()+60*this.utcOffset()*1e3).toISOString().replace("Z",ue(a,"Z")):ue(a,e?"YYYY-MM-DD[T]HH:mm:ss.SSS[Z]":"YYYY-MM-DD[T]HH:mm:ss.SSSZ")},n.inspect=function(){if(!this.isValid())return"moment.invalid(/* "+this._i+" */)";var e,a="moment",t="";return this.isLocal()||(a=0===this.utcOffset()?"moment.utc":"moment.parseZone",t="Z"),a="["+a+'("]',e=0<=this.year()&&this.year()<=9999?"YYYY":"YYYYYY",this.format(a+e+"-MM-DD[T]HH:mm:ss.SSS"+(t+'[")]'))},"undefined"!=typeof Symbol&&null!=Symbol.for&&(n[Symbol.for("nodejs.util.inspect.custom")]=function(){return"Moment<"+this.format()+">"}),n.toJSON=function(){return this.isValid()?this.toISOString():null},n.toString=function(){return this.clone().locale("en").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ")},n.unix=function(){return Math.floor(this.valueOf()/1e3)},n.valueOf=function(){return this._d.valueOf()-6e4*(this._offset||0)},n.creationData=function(){return{input:this._i,format:this._f,locale:this._locale,isUTC:this._isUTC,strict:this._strict}},n.eraName=function(){for(var e,a=this.localeData().eras(),t=0,s=a.length;t<s;++t){if(e=this.clone().startOf("day").valueOf(),a[t].since<=e&&e<=a[t].until)return a[t].name;if(a[t].until<=e&&e<=a[t].since)return a[t].name}return""},n.eraNarrow=function(){for(var e,a=this.localeData().eras(),t=0,s=a.length;t<s;++t){if(e=this.clone().startOf("day").valueOf(),a[t].since<=e&&e<=a[t].until)return a[t].narrow;if(a[t].until<=e&&e<=a[t].since)return a[t].narrow}return""},n.eraAbbr=function(){for(var e,a=this.localeData().eras(),t=0,s=a.length;t<s;++t){if(e=this.clone().startOf("day").valueOf(),a[t].since<=e&&e<=a[t].until)return a[t].abbr;if(a[t].until<=e&&e<=a[t].since)return a[t].abbr}return""},n.eraYear=function(){for(var e,a,t=this.localeData().eras(),s=0,n=t.length;s<n;++s)if(e=t[s].since<=t[s].until?1:-1,a=this.clone().startOf("day").valueOf(),t[s].since<=a&&a<=t[s].until||t[s].until<=a&&a<=t[s].since)return(this.year()-M(t[s].since).year())*e+t[s].offset;return this.year()},n.year=Ke,n.isLeapYear=function(){return Le(this.year())},n.weekYear=function(e){return ht.call(this,e,this.week(),this.weekday(),this.localeData()._week.dow,this.localeData()._week.doy)},n.isoWeekYear=function(e){return ht.call(this,e,this.isoWeek(),this.isoWeekday(),1,4)},n.quarter=n.quarters=function(e){return null==e?Math.ceil((this.month()+1)/3):this.month(3*(e-1)+this.month()%3)},n.month=Ge,n.daysInMonth=function(){return ze(this.year(),this.month())},n.week=n.weeks=function(e){var a=this.localeData().week(this);return null==e?a:this.add(7*(e-a),"d")},n.isoWeek=n.isoWeeks=function(e){var a=Xe(this,1,4).week;return null==e?a:this.add(7*(e-a),"d")},n.weeksInYear=function(){var e=this.localeData()._week;return ea(this.year(),e.dow,e.doy)},n.weeksInWeekYear=function(){var e=this.localeData()._week;return ea(this.weekYear(),e.dow,e.doy)},n.isoWeeksInYear=function(){return ea(this.year(),1,4)},n.isoWeeksInISOWeekYear=function(){return ea(this.isoWeekYear(),1,4)},n.date=Y,n.day=n.days=function(e){if(!this.isValid())return null!=e?this:NaN;var a,t,s=this._isUTC?this._d.getUTCDay():this._d.getDay();return null!=e?(a=e,t=this.localeData(),e="string"!=typeof a?a:isNaN(a)?"number"==typeof(a=t.weekdaysParse(a))?a:null:parseInt(a,10),this.add(e-s,"d")):s},n.weekday=function(e){if(!this.isValid())return null!=e?this:NaN;var a=(this.day()+7-this.localeData()._week.dow)%7;return null==e?a:this.add(e-a,"d")},n.isoWeekday=function(e){return this.isValid()?null!=e?(a=e,t=this.localeData(),t="string"==typeof a?t.weekdaysParse(a)%7||7:isNaN(a)?null:a,this.day(this.day()%7?t:t-7)):this.day()||7:null!=e?this:NaN;var a,t},n.dayOfYear=function(e){var a=Math.round((this.clone().startOf("day")-this.clone().startOf("year"))/864e5)+1;return null==e?a:this.add(e-a,"d")},n.hour=n.hours=f,n.minute=n.minutes=ke,n.second=n.seconds=y,n.millisecond=n.milliseconds=_,n.utcOffset=function(e,a,t){var s,n=this._offset||0;if(!this.isValid())return null!=e?this:NaN;if(null==e)return this._isUTC?n:Ba(this);if("string"==typeof e){if(null===(e=Ga(be,e)))return this}else Math.abs(e)<16&&!t&&(e*=60);return!this._isUTC&&a&&(s=Ba(this)),this._offset=e,this._isUTC=!0,null!=s&&this.add(s,"m"),n!==e&&(!a||this._changeInProgress?at(this,$a(e-n,"m"),1,!1):this._changeInProgress||(this._changeInProgress=!0,M.updateOffset(this,!0),this._changeInProgress=null)),this},n.utc=function(e){return this.utcOffset(0,e)},n.local=function(e){return this._isUTC&&(this.utcOffset(0,e),this._isUTC=!1,e&&this.subtract(Ba(this),"m")),this},n.parseZone=function(){var e;return null!=this._tzm?this.utcOffset(this._tzm,!1,!0):"string"==typeof this._i&&(null!=(e=Ga(we,this._i))?this.utcOffset(e):this.utcOffset(0,!0)),this},n.hasAlignedHourOffset=function(e){return!!this.isValid()&&(e=e?S(e).utcOffset():0,(this.utcOffset()-e)%60==0)},n.isDST=function(){return this.utcOffset()>this.clone().month(0).utcOffset()||this.utcOffset()>this.clone().month(5).utcOffset()},n.isLocal=function(){return!!this.isValid()&&!this._isUTC},n.isUtcOffset=function(){return!!this.isValid()&&this._isUTC},n.isUtc=Ka,n.isUTC=Ka,n.zoneAbbr=function(){return this._isUTC?"UTC":""},n.zoneName=function(){return this._isUTC?"Coordinated Universal Time":""},n.dates=e("dates accessor is deprecated. Use date instead.",Y),n.months=e("months accessor is deprecated. Use month instead",Ge),n.years=e("years accessor is deprecated. Use year instead",Ke),n.zone=e("moment().zone is deprecated, use moment().utcOffset instead. http://momentjs.com/guides/#/warnings/zone/",function(e,a){return null!=e?(this.utcOffset(e="string"!=typeof e?-e:e,a),this):-this.utcOffset()}),n.isDSTShifted=e("isDSTShifted is deprecated. See http://momentjs.com/guides/#/warnings/dst-shifted/ for more information",function(){if(!d(this._isDSTShifted))return this._isDSTShifted;var e,a={};return Z(a,this),(a=Ea(a))._a?(e=(a._isUTC?U:S)(a._a),this._isDSTShifted=this.isValid()&&0<function(e,a,t){for(var s=Math.min(e.length,a.length),n=Math.abs(e.length-a.length),r=0,d=0;d<s;d++)(t&&e[d]!==a[d]||!t&&m(e[d])!==m(a[d]))&&r++;return r+n}(a._a,e.toArray())):this._isDSTShifted=!1,this._isDSTShifted});r=ne.prototype;function yt(e,a,t,s){var n=pa(),s=U().set(s,a);return n[t](s,e)}function ft(e,a,t){if(J(e)&&(a=e,e=void 0),e=e||"",null!=a)return yt(e,a,t,"month");for(var s=[],n=0;n<12;n++)s[n]=yt(e,n,t,"month");return s}function pt(e,a,t,s){a=("boolean"==typeof e?J(a)&&(t=a,a=void 0):(a=e,e=!1,J(t=a)&&(t=a,a=void 0)),a||"");var n,r=pa(),d=e?r._week.dow:0,i=[];if(null!=t)return yt(a,(t+d)%7,s,"day");for(n=0;n<7;n++)i[n]=yt(a,(n+d)%7,s,"day");return i}r.calendar=function(e,a,t){return te(e=this._calendar[e]||this._calendar.sameElse)?e.call(a,t):e},r.longDateFormat=function(e){var a=this._longDateFormat[e],t=this._longDateFormat[e.toUpperCase()];return a||!t?a:(this._longDateFormat[e]=t.match(ie).map(function(e){return"MMMM"===e||"MM"===e||"DD"===e||"dddd"===e?e.slice(1):e}).join(""),this._longDateFormat[e])},r.invalidDate=function(){return this._invalidDate},r.ordinal=function(e){return this._ordinal.replace("%d",e)},r.preparse=Yt,r.postformat=Yt,r.relativeTime=function(e,a,t,s){var n=this._relativeTime[t];return te(n)?n(e,a,t,s):n.replace(/%d/i,e)},r.pastFuture=function(e,a){return te(e=this._relativeTime[0<e?"future":"past"])?e(a):e.replace(/%s/i,a)},r.set=function(e){var a,t;for(t in e)l(e,t)&&(te(a=e[t])?this[t]=a:this["_"+t]=a);this._config=e,this._dayOfMonthOrdinalParseLenient=new RegExp((this._dayOfMonthOrdinalParse.source||this._ordinalParse.source)+"|"+/\d{1,2}/.source)},r.eras=function(e,a){for(var t,s=this._eras||pa("en")._eras,n=0,r=s.length;n<r;++n){switch(typeof s[n].since){case"string":t=M(s[n].since).startOf("day"),s[n].since=t.valueOf();break}switch(typeof s[n].until){case"undefined":s[n].until=1/0;break;case"string":t=M(s[n].until).startOf("day").valueOf(),s[n].until=t.valueOf();break}}return s},r.erasParse=function(e,a,t){var s,n,r,d,i,_=this.eras();for(e=e.toUpperCase(),s=0,n=_.length;s<n;++s)if(r=_[s].name.toUpperCase(),d=_[s].abbr.toUpperCase(),i=_[s].narrow.toUpperCase(),t)switch(a){case"N":case"NN":case"NNN":if(d===e)return _[s];break;case"NNNN":if(r===e)return _[s];break;case"NNNNN":if(i===e)return _[s];break}else if(0<=[r,d,i].indexOf(e))return _[s]},r.erasConvertYear=function(e,a){var t=e.since<=e.until?1:-1;return void 0===a?M(e.since).year():M(e.since).year()+(a-e.offset)*t},r.erasAbbrRegex=function(e){return l(this,"_erasAbbrRegex")||lt.call(this),e?this._erasAbbrRegex:this._erasRegex},r.erasNameRegex=function(e){return l(this,"_erasNameRegex")||lt.call(this),e?this._erasNameRegex:this._erasRegex},r.erasNarrowRegex=function(e){return l(this,"_erasNarrowRegex")||lt.call(this),e?this._erasNarrowRegex:this._erasRegex},r.months=function(e,a){return e?(F(this._months)?this._months:this._months[(this._months.isFormat||Re).test(a)?"format":"standalone"])[e.month()]:F(this._months)?this._months:this._months.standalone},r.monthsShort=function(e,a){return e?(F(this._monthsShort)?this._monthsShort:this._monthsShort[Re.test(a)?"format":"standalone"])[e.month()]:F(this._monthsShort)?this._monthsShort:this._monthsShort.standalone},r.monthsParse=function(e,a,t){var s,n;if(this._monthsParseExact)return function(e,a,t){var s,n,r,e=e.toLocaleLowerCase();if(!this._monthsParse)for(this._monthsParse=[],this._longMonthsParse=[],this._shortMonthsParse=[],s=0;s<12;++s)r=U([2e3,s]),this._shortMonthsParse[s]=this.monthsShort(r,"").toLocaleLowerCase(),this._longMonthsParse[s]=this.months(r,"").toLocaleLowerCase();return t?"MMM"===a?-1!==(n=T.call(this._shortMonthsParse,e))?n:null:-1!==(n=T.call(this._longMonthsParse,e))?n:null:"MMM"===a?-1!==(n=T.call(this._shortMonthsParse,e))||-1!==(n=T.call(this._longMonthsParse,e))?n:null:-1!==(n=T.call(this._longMonthsParse,e))||-1!==(n=T.call(this._shortMonthsParse,e))?n:null}.call(this,e,a,t);for(this._monthsParse||(this._monthsParse=[],this._longMonthsParse=[],this._shortMonthsParse=[]),s=0;s<12;s++){if(n=U([2e3,s]),t&&!this._longMonthsParse[s]&&(this._longMonthsParse[s]=new RegExp("^"+this.months(n,"").replace(".","")+"$","i"),this._shortMonthsParse[s]=new RegExp("^"+this.monthsShort(n,"").replace(".","")+"$","i")),t||this._monthsParse[s]||(n="^"+this.months(n,"")+"|^"+this.monthsShort(n,""),this._monthsParse[s]=new RegExp(n.replace(".",""),"i")),t&&"MMMM"===a&&this._longMonthsParse[s].test(e))return s;if(t&&"MMM"===a&&this._shortMonthsParse[s].test(e))return s;if(!t&&this._monthsParse[s].test(e))return s}},r.monthsRegex=function(e){return this._monthsParseExact?(l(this,"_monthsRegex")||Ve.call(this),e?this._monthsStrictRegex:this._monthsRegex):(l(this,"_monthsRegex")||(this._monthsRegex=Ie),this._monthsStrictRegex&&e?this._monthsStrictRegex:this._monthsRegex)},r.monthsShortRegex=function(e){return this._monthsParseExact?(l(this,"_monthsRegex")||Ve.call(this),e?this._monthsShortStrictRegex:this._monthsShortRegex):(l(this,"_monthsShortRegex")||(this._monthsShortRegex=Ce),this._monthsShortStrictRegex&&e?this._monthsShortStrictRegex:this._monthsShortRegex)},r.week=function(e){return Xe(e,this._week.dow,this._week.doy).week},r.firstDayOfYear=function(){return this._week.doy},r.firstDayOfWeek=function(){return this._week.dow},r.weekdays=function(e,a){return a=F(this._weekdays)?this._weekdays:this._weekdays[e&&!0!==e&&this._weekdays.isFormat.test(a)?"format":"standalone"],!0===e?aa(a,this._week.dow):e?a[e.day()]:a},r.weekdaysMin=function(e){return!0===e?aa(this._weekdaysMin,this._week.dow):e?this._weekdaysMin[e.day()]:this._weekdaysMin},r.weekdaysShort=function(e){return!0===e?aa(this._weekdaysShort,this._week.dow):e?this._weekdaysShort[e.day()]:this._weekdaysShort},r.weekdaysParse=function(e,a,t){var s,n;if(this._weekdaysParseExact)return function(e,a,t){var s,n,r,e=e.toLocaleLowerCase();if(!this._weekdaysParse)for(this._weekdaysParse=[],this._shortWeekdaysParse=[],this._minWeekdaysParse=[],s=0;s<7;++s)r=U([2e3,1]).day(s),this._minWeekdaysParse[s]=this.weekdaysMin(r,"").toLocaleLowerCase(),this._shortWeekdaysParse[s]=this.weekdaysShort(r,"").toLocaleLowerCase(),this._weekdaysParse[s]=this.weekdays(r,"").toLocaleLowerCase();return t?"dddd"===a?-1!==(n=T.call(this._weekdaysParse,e))?n:null:"ddd"===a?-1!==(n=T.call(this._shortWeekdaysParse,e))?n:null:-1!==(n=T.call(this._minWeekdaysParse,e))?n:null:"dddd"===a?-1!==(n=T.call(this._weekdaysParse,e))||-1!==(n=T.call(this._shortWeekdaysParse,e))||-1!==(n=T.call(this._minWeekdaysParse,e))?n:null:"ddd"===a?-1!==(n=T.call(this._shortWeekdaysParse,e))||-1!==(n=T.call(this._weekdaysParse,e))||-1!==(n=T.call(this._minWeekdaysParse,e))?n:null:-1!==(n=T.call(this._minWeekdaysParse,e))||-1!==(n=T.call(this._weekdaysParse,e))||-1!==(n=T.call(this._shortWeekdaysParse,e))?n:null}.call(this,e,a,t);for(this._weekdaysParse||(this._weekdaysParse=[],this._minWeekdaysParse=[],this._shortWeekdaysParse=[],this._fullWeekdaysParse=[]),s=0;s<7;s++){if(n=U([2e3,1]).day(s),t&&!this._fullWeekdaysParse[s]&&(this._fullWeekdaysParse[s]=new RegExp("^"+this.weekdays(n,"").replace(".","\\.?")+"$","i"),this._shortWeekdaysParse[s]=new RegExp("^"+this.weekdaysShort(n,"").replace(".","\\.?")+"$","i"),this._minWeekdaysParse[s]=new RegExp("^"+this.weekdaysMin(n,"").replace(".","\\.?")+"$","i")),this._weekdaysParse[s]||(n="^"+this.weekdays(n,"")+"|^"+this.weekdaysShort(n,"")+"|^"+this.weekdaysMin(n,""),this._weekdaysParse[s]=new RegExp(n.replace(".",""),"i")),t&&"dddd"===a&&this._fullWeekdaysParse[s].test(e))return s;if(t&&"ddd"===a&&this._shortWeekdaysParse[s].test(e))return s;if(t&&"dd"===a&&this._minWeekdaysParse[s].test(e))return s;if(!t&&this._weekdaysParse[s].test(e))return s}},r.weekdaysRegex=function(e){return this._weekdaysParseExact?(l(this,"_weekdaysRegex")||_a.call(this),e?this._weekdaysStrictRegex:this._weekdaysRegex):(l(this,"_weekdaysRegex")||(this._weekdaysRegex=ra),this._weekdaysStrictRegex&&e?this._weekdaysStrictRegex:this._weekdaysRegex)},r.weekdaysShortRegex=function(e){return this._weekdaysParseExact?(l(this,"_weekdaysRegex")||_a.call(this),e?this._weekdaysShortStrictRegex:this._weekdaysShortRegex):(l(this,"_weekdaysShortRegex")||(this._weekdaysShortRegex=da),this._weekdaysShortStrictRegex&&e?this._weekdaysShortStrictRegex:this._weekdaysShortRegex)},r.weekdaysMinRegex=function(e){return this._weekdaysParseExact?(l(this,"_weekdaysRegex")||_a.call(this),e?this._weekdaysMinStrictRegex:this._weekdaysMinRegex):(l(this,"_weekdaysMinRegex")||(this._weekdaysMinRegex=ia),this._weekdaysMinStrictRegex&&e?this._weekdaysMinStrictRegex:this._weekdaysMinRegex)},r.isPM=function(e){return"p"===(e+"").toLowerCase().charAt(0)},r.meridiem=function(e,a,t){return 11<e?t?"pm":"PM":t?"am":"AM"},ya("en",{eras:[{since:"0001-01-01",until:1/0,offset:1,name:"Anno Domini",narrow:"AD",abbr:"AD"},{since:"0000-12-31",until:-1/0,offset:1,name:"Before Christ",narrow:"BC",abbr:"BC"}],dayOfMonthOrdinalParse:/\d{1,2}(th|st|nd|rd)/,ordinal:function(e){var a=e%10;return e+(1===m(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")}}),M.lang=e("moment.lang is deprecated. Use moment.locale instead.",ya),M.langData=e("moment.langData is deprecated. Use moment.localeData instead.",pa);var kt=Math.abs;function Dt(e,a,t,s){a=$a(a,t);return e._milliseconds+=s*a._milliseconds,e._days+=s*a._days,e._months+=s*a._months,e._bubble()}function Tt(e){return e<0?Math.floor(e):Math.ceil(e)}function gt(e){return 4800*e/146097}function wt(e){return 146097*e/4800}function bt(e){return function(){return this.as(e)}}De=bt("ms"),pe=bt("s"),Ne=bt("m"),L=bt("h"),c=bt("d"),ta=bt("w"),f=bt("M"),ke=bt("Q"),y=bt("y");function St(e){return function(){return this.isValid()?this._data[e]:NaN}}var _=St("milliseconds"),Y=St("seconds"),Ke=St("minutes"),r=St("hours"),Ht=St("days"),vt=St("months"),jt=St("years");var xt=Math.round,Pt={ss:44,s:45,m:45,h:22,d:26,w:null,M:11};function Ot(e,a,t,s){var n=$a(e).abs(),r=xt(n.as("s")),d=xt(n.as("m")),i=xt(n.as("h")),_=xt(n.as("d")),o=xt(n.as("M")),m=xt(n.as("w")),n=xt(n.as("y")),r=(r<=t.ss?["s",r]:r<t.s&&["ss",r])||d<=1&&["m"]||d<t.m&&["mm",d]||i<=1&&["h"]||i<t.h&&["hh",i]||_<=1&&["d"]||_<t.d&&["dd",_];return(r=(r=null!=t.w?r||m<=1&&["w"]||m<t.w&&["ww",m]:r)||o<=1&&["M"]||o<t.M&&["MM",o]||n<=1&&["y"]||["yy",n])[2]=a,r[3]=0<+e,r[4]=s,function(e,a,t,s,n){return n.relativeTime(a||1,!!t,e,s)}.apply(null,r)}var Wt=Math.abs;function At(e){return(0<e)-(e<0)||+e}function Et(){if(!this.isValid())return this.localeData().invalidDate();var e,a,t,s,n,r,d,i=Wt(this._milliseconds)/1e3,_=Wt(this._days),o=Wt(this._months),m=this.asSeconds();return m?(e=u(i/60),a=u(e/60),i%=60,e%=60,t=u(o/12),o%=12,s=i?i.toFixed(3).replace(/\.?0+$/,""):"",n=At(this._months)!==At(m)?"-":"",r=At(this._days)!==At(m)?"-":"",d=At(this._milliseconds)!==At(m)?"-":"",(m<0?"-":"")+"P"+(t?n+t+"Y":"")+(o?n+o+"M":"")+(_?r+_+"D":"")+(a||e||i?"T":"")+(a?d+a+"H":"")+(e?d+e+"M":"")+(i?d+s+"S":"")):"P0D"}function Ft(e){return 0===e?0:1===e?1:2===e?2:3<=e%100&&e%100<=10?3:11<=e%100?4:5}function H(d){return function(e,a,t,s){var n=Ft(e),r=Jt[d][Ft(e)];return(r=2===n?r[a?0:1]:r).replace(/%d/i,e)}}function zt(e){return 0===e?0:1===e?1:2===e?2:3<=e%100&&e%100<=10?3:11<=e%100?4:5}function v(d){return function(e,a,t,s){var n=zt(e),r=Ct[d][zt(e)];return(r=2===n?r[a?0:1]:r).replace(/%d/i,e)}}function Nt(e){return 0===e?0:1===e?1:2===e?2:3<=e%100&&e%100<=10?3:11<=e%100?4:5}function j(d){return function(e,a,t,s){var n=Nt(e),r=Bt[d][Nt(e)];return(r=2===n?r[a?0:1]:r).replace(/%d/i,e)}}var x=Ja.prototype,Jt=(x.isValid=function(){return this._isValid},x.abs=function(){var e=this._data;return this._milliseconds=kt(this._milliseconds),this._days=kt(this._days),this._months=kt(this._months),e.milliseconds=kt(e.milliseconds),e.seconds=kt(e.seconds),e.minutes=kt(e.minutes),e.hours=kt(e.hours),e.months=kt(e.months),e.years=kt(e.years),this},x.add=function(e,a){return Dt(this,e,a,1)},x.subtract=function(e,a){return Dt(this,e,a,-1)},x.as=function(e){if(!this.isValid())return NaN;var a,t,s=this._milliseconds;if("month"===(e=i(e))||"quarter"===e||"year"===e)switch(a=this._days+s/864e5,t=this._months+gt(a),e){case"month":return t;case"quarter":return t/3;case"year":return t/12}else switch(a=this._days+Math.round(wt(this._months)),e){case"week":return a/7+s/6048e5;case"day":return a+s/864e5;case"hour":return 24*a+s/36e5;case"minute":return 1440*a+s/6e4;case"second":return 86400*a+s/1e3;case"millisecond":return Math.floor(864e5*a)+s;default:throw new Error("Unknown unit "+e)}},x.asMilliseconds=De,x.asSeconds=pe,x.asMinutes=Ne,x.asHours=L,x.asDays=c,x.asWeeks=ta,x.asMonths=f,x.asQuarters=ke,x.asYears=y,x.valueOf=function(){return this.isValid()?this._milliseconds+864e5*this._days+this._months%12*2592e6+31536e6*m(this._months/12):NaN},x._bubble=function(){var e=this._milliseconds,a=this._days,t=this._months,s=this._data;return 0<=e&&0<=a&&0<=t||e<=0&&a<=0&&t<=0||(e+=864e5*Tt(wt(t)+a),t=a=0),s.milliseconds=e%1e3,e=u(e/1e3),s.seconds=e%60,e=u(e/60),s.minutes=e%60,e=u(e/60),s.hours=e%24,a+=u(e/24),t+=e=u(gt(a)),a-=Tt(wt(e)),e=u(t/12),t%=12,s.days=a,s.months=t,s.years=e,this},x.clone=function(){return $a(this)},x.get=function(e){return e=i(e),this.isValid()?this[e+"s"]():NaN},x.milliseconds=_,x.seconds=Y,x.minutes=Ke,x.hours=r,x.days=Ht,x.weeks=function(){return u(this.days()/7)},x.months=vt,x.years=jt,x.humanize=function(e,a){if(!this.isValid())return this.localeData().invalidDate();var t=!1,s=Pt;return"object"==typeof e&&(a=e,e=!1),"boolean"==typeof e&&(t=e),"object"==typeof a&&(s=Object.assign({},Pt,a),null!=a.s&&null==a.ss&&(s.ss=a.s-1)),e=this.localeData(),a=Ot(this,!t,s,e),t&&(a=e.pastFuture(+this,a)),e.postformat(a)},x.toISOString=Et,x.toString=Et,x.toJSON=Et,x.locale=rt,x.localeData=dt,x.toIsoString=e("toIsoString() is deprecated. Please use toISOString() instead (notice the capitals)",Et),x.lang=na,s("X",0,0,"unix"),s("x",0,0,"valueOf"),p("x",ge),p("X",/[+-]?\d+(\.\d{1,3})?/),D("X",function(e,a,t){t._d=new Date(1e3*parseFloat(e))}),D("x",function(e,a,t){t._d=new Date(m(e))}),M.version="2.29.2",E=S,M.fn=n,M.min=function(){return za("isBefore",[].slice.call(arguments,0))},M.max=function(){return za("isAfter",[].slice.call(arguments,0))},M.now=function(){return Date.now?Date.now():+new Date},M.utc=U,M.unix=function(e){return S(1e3*e)},M.months=function(e,a){return ft(e,a,"months")},M.isDate=R,M.locale=ya,M.invalid=V,M.duration=$a,M.isMoment=Q,M.weekdays=function(e,a,t){return pt(e,a,t,"weekdays")},M.parseZone=function(){return S.apply(null,arguments).parseZone()},M.localeData=pa,M.isDuration=Ra,M.monthsShort=function(e,a){return ft(e,a,"monthsShort")},M.weekdaysMin=function(e,a,t){return pt(e,a,t,"weekdaysMin")},M.defineLocale=fa,M.updateLocale=function(e,a){var t,s;return null!=a?(s=Ma,null!=b[e]&&null!=b[e].parentLocale?b[e].set(se(b[e]._config,a)):(a=se(s=null!=(t=Ya(e))?t._config:s,a),null==t&&(a.abbr=e),(s=new ne(a)).parentLocale=b[e],b[e]=s),ya(e)):null!=b[e]&&(null!=b[e].parentLocale?(b[e]=b[e].parentLocale,e===ya()&&ya(e)):null!=b[e]&&delete b[e]),b[e]},M.locales=function(){return re(b)},M.weekdaysShort=function(e,a,t){return pt(e,a,t,"weekdaysShort")},M.normalizeUnits=i,M.relativeTimeRounding=function(e){return void 0===e?xt:"function"==typeof e&&(xt=e,!0)},M.relativeTimeThreshold=function(e,a){return void 0!==Pt[e]&&(void 0===a?Pt[e]:(Pt[e]=a,"s"===e&&(Pt.ss=a-1),!0))},M.calendarFormat=function(e,a){return(e=e.diff(a,"days",!0))<-6?"sameElse":e<-1?"lastWeek":e<0?"lastDay":e<1?"sameDay":e<2?"nextDay":e<7?"nextWeek":"sameElse"},M.prototype=n,M.HTML5_FMT={DATETIME_LOCAL:"YYYY-MM-DDTHH:mm",DATETIME_LOCAL_SECONDS:"YYYY-MM-DDTHH:mm:ss",DATETIME_LOCAL_MS:"YYYY-MM-DDTHH:mm:ss.SSS",DATE:"YYYY-MM-DD",TIME:"HH:mm",TIME_SECONDS:"HH:mm:ss",TIME_MS:"HH:mm:ss.SSS",WEEK:"GGGG-[W]WW",MONTH:"YYYY-MM"},M.defineLocale("af",{months:"Januarie_Februarie_Maart_April_Mei_Junie_Julie_Augustus_September_Oktober_November_Desember".split("_"),monthsShort:"Jan_Feb_Mrt_Apr_Mei_Jun_Jul_Aug_Sep_Okt_Nov_Des".split("_"),weekdays:"Sondag_Maandag_Dinsdag_Woensdag_Donderdag_Vrydag_Saterdag".split("_"),weekdaysShort:"Son_Maa_Din_Woe_Don_Vry_Sat".split("_"),weekdaysMin:"So_Ma_Di_Wo_Do_Vr_Sa".split("_"),meridiemParse:/vm|nm/i,isPM:function(e){return/^nm$/i.test(e)},meridiem:function(e,a,t){return e<12?t?"vm":"VM":t?"nm":"NM"},longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Vandag om] LT",nextDay:"[M\xf4re om] LT",nextWeek:"dddd [om] LT",lastDay:"[Gister om] LT",lastWeek:"[Laas] dddd [om] LT",sameElse:"L"},relativeTime:{future:"oor %s",past:"%s gelede",s:"'n paar sekondes",ss:"%d sekondes",m:"'n minuut",mm:"%d minute",h:"'n uur",hh:"%d ure",d:"'n dag",dd:"%d dae",M:"'n maand",MM:"%d maande",y:"'n jaar",yy:"%d jaar"},dayOfMonthOrdinalParse:/\d{1,2}(ste|de)/,ordinal:function(e){return e+(1===e||8===e||20<=e?"ste":"de")},week:{dow:1,doy:4}}),{s:["\u0623\u0642\u0644 \u0645\u0646 \u062b\u0627\u0646\u064a\u0629","\u062b\u0627\u0646\u064a\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062b\u0627\u0646\u064a\u062a\u0627\u0646","\u062b\u0627\u0646\u064a\u062a\u064a\u0646"],"%d \u062b\u0648\u0627\u0646","%d \u062b\u0627\u0646\u064a\u0629","%d \u062b\u0627\u0646\u064a\u0629"],m:["\u0623\u0642\u0644 \u0645\u0646 \u062f\u0642\u064a\u0642\u0629","\u062f\u0642\u064a\u0642\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062f\u0642\u064a\u0642\u062a\u0627\u0646","\u062f\u0642\u064a\u0642\u062a\u064a\u0646"],"%d \u062f\u0642\u0627\u0626\u0642","%d \u062f\u0642\u064a\u0642\u0629","%d \u062f\u0642\u064a\u0642\u0629"],h:["\u0623\u0642\u0644 \u0645\u0646 \u0633\u0627\u0639\u0629","\u0633\u0627\u0639\u0629 \u0648\u0627\u062d\u062f\u0629",["\u0633\u0627\u0639\u062a\u0627\u0646","\u0633\u0627\u0639\u062a\u064a\u0646"],"%d \u0633\u0627\u0639\u0627\u062a","%d \u0633\u0627\u0639\u0629","%d \u0633\u0627\u0639\u0629"],d:["\u0623\u0642\u0644 \u0645\u0646 \u064a\u0648\u0645","\u064a\u0648\u0645 \u0648\u0627\u062d\u062f",["\u064a\u0648\u0645\u0627\u0646","\u064a\u0648\u0645\u064a\u0646"],"%d \u0623\u064a\u0627\u0645","%d \u064a\u0648\u0645\u064b\u0627","%d \u064a\u0648\u0645"],M:["\u0623\u0642\u0644 \u0645\u0646 \u0634\u0647\u0631","\u0634\u0647\u0631 \u0648\u0627\u062d\u062f",["\u0634\u0647\u0631\u0627\u0646","\u0634\u0647\u0631\u064a\u0646"],"%d \u0623\u0634\u0647\u0631","%d \u0634\u0647\u0631\u0627","%d \u0634\u0647\u0631"],y:["\u0623\u0642\u0644 \u0645\u0646 \u0639\u0627\u0645","\u0639\u0627\u0645 \u0648\u0627\u062d\u062f",["\u0639\u0627\u0645\u0627\u0646","\u0639\u0627\u0645\u064a\u0646"],"%d \u0623\u0639\u0648\u0627\u0645","%d \u0639\u0627\u0645\u064b\u0627","%d \u0639\u0627\u0645"]}),De=["\u062c\u0627\u0646\u0641\u064a","\u0641\u064a\u0641\u0631\u064a","\u0645\u0627\u0631\u0633","\u0623\u0641\u0631\u064a\u0644","\u0645\u0627\u064a","\u062c\u0648\u0627\u0646","\u062c\u0648\u064a\u0644\u064a\u0629","\u0623\u0648\u062a","\u0633\u0628\u062a\u0645\u0628\u0631","\u0623\u0643\u062a\u0648\u0628\u0631","\u0646\u0648\u0641\u0645\u0628\u0631","\u062f\u064a\u0633\u0645\u0628\u0631"],Rt=(M.defineLocale("ar-dz",{months:De,monthsShort:De,weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0623\u062d\u062f_\u0625\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0623\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"D/\u200fM/\u200fYYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0635|\u0645/,isPM:function(e){return"\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635":"\u0645"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u064b\u0627 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0628\u0639\u062f %s",past:"\u0645\u0646\u0630 %s",s:H("s"),ss:H("s"),m:H("m"),mm:H("m"),h:H("h"),hh:H("h"),d:H("d"),dd:H("d"),M:H("M"),MM:H("M"),y:H("y"),yy:H("y")},postformat:function(e){return e.replace(/,/g,"\u060c")},week:{dow:0,doy:4}}),M.defineLocale("ar-kw",{months:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648\u0632_\u063a\u0634\u062a_\u0634\u062a\u0646\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0646\u0628\u0631_\u062f\u062c\u0646\u0628\u0631".split("_"),monthsShort:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648\u0632_\u063a\u0634\u062a_\u0634\u062a\u0646\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0646\u0628\u0631_\u062f\u062c\u0646\u0628\u0631".split("_"),weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062a\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0627\u062d\u062f_\u0627\u062a\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u0627 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0641\u064a %s",past:"\u0645\u0646\u0630 %s",s:"\u062b\u0648\u0627\u0646",ss:"%d \u062b\u0627\u0646\u064a\u0629",m:"\u062f\u0642\u064a\u0642\u0629",mm:"%d \u062f\u0642\u0627\u0626\u0642",h:"\u0633\u0627\u0639\u0629",hh:"%d \u0633\u0627\u0639\u0627\u062a",d:"\u064a\u0648\u0645",dd:"%d \u0623\u064a\u0627\u0645",M:"\u0634\u0647\u0631",MM:"%d \u0623\u0634\u0647\u0631",y:"\u0633\u0646\u0629",yy:"%d \u0633\u0646\u0648\u0627\u062a"},week:{dow:0,doy:12}}),{1:"1",2:"2",3:"3",4:"4",5:"5",6:"6",7:"7",8:"8",9:"9",0:"0"}),Ct={s:["\u0623\u0642\u0644 \u0645\u0646 \u062b\u0627\u0646\u064a\u0629","\u062b\u0627\u0646\u064a\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062b\u0627\u0646\u064a\u062a\u0627\u0646","\u062b\u0627\u0646\u064a\u062a\u064a\u0646"],"%d \u062b\u0648\u0627\u0646","%d \u062b\u0627\u0646\u064a\u0629","%d \u062b\u0627\u0646\u064a\u0629"],m:["\u0623\u0642\u0644 \u0645\u0646 \u062f\u0642\u064a\u0642\u0629","\u062f\u0642\u064a\u0642\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062f\u0642\u064a\u0642\u062a\u0627\u0646","\u062f\u0642\u064a\u0642\u062a\u064a\u0646"],"%d \u062f\u0642\u0627\u0626\u0642","%d \u062f\u0642\u064a\u0642\u0629","%d \u062f\u0642\u064a\u0642\u0629"],h:["\u0623\u0642\u0644 \u0645\u0646 \u0633\u0627\u0639\u0629","\u0633\u0627\u0639\u0629 \u0648\u0627\u062d\u062f\u0629",["\u0633\u0627\u0639\u062a\u0627\u0646","\u0633\u0627\u0639\u062a\u064a\u0646"],"%d \u0633\u0627\u0639\u0627\u062a","%d \u0633\u0627\u0639\u0629","%d \u0633\u0627\u0639\u0629"],d:["\u0623\u0642\u0644 \u0645\u0646 \u064a\u0648\u0645","\u064a\u0648\u0645 \u0648\u0627\u062d\u062f",["\u064a\u0648\u0645\u0627\u0646","\u064a\u0648\u0645\u064a\u0646"],"%d \u0623\u064a\u0627\u0645","%d \u064a\u0648\u0645\u064b\u0627","%d \u064a\u0648\u0645"],M:["\u0623\u0642\u0644 \u0645\u0646 \u0634\u0647\u0631","\u0634\u0647\u0631 \u0648\u0627\u062d\u062f",["\u0634\u0647\u0631\u0627\u0646","\u0634\u0647\u0631\u064a\u0646"],"%d \u0623\u0634\u0647\u0631","%d \u0634\u0647\u0631\u0627","%d \u0634\u0647\u0631"],y:["\u0623\u0642\u0644 \u0645\u0646 \u0639\u0627\u0645","\u0639\u0627\u0645 \u0648\u0627\u062d\u062f",["\u0639\u0627\u0645\u0627\u0646","\u0639\u0627\u0645\u064a\u0646"],"%d \u0623\u0639\u0648\u0627\u0645","%d \u0639\u0627\u0645\u064b\u0627","%d \u0639\u0627\u0645"]},pe=["\u064a\u0646\u0627\u064a\u0631","\u0641\u0628\u0631\u0627\u064a\u0631","\u0645\u0627\u0631\u0633","\u0623\u0628\u0631\u064a\u0644","\u0645\u0627\u064a\u0648","\u064a\u0648\u0646\u064a\u0648","\u064a\u0648\u0644\u064a\u0648","\u0623\u063a\u0633\u0637\u0633","\u0633\u0628\u062a\u0645\u0628\u0631","\u0623\u0643\u062a\u0648\u0628\u0631","\u0646\u0648\u0641\u0645\u0628\u0631","\u062f\u064a\u0633\u0645\u0628\u0631"],It=(M.defineLocale("ar-ly",{months:pe,monthsShort:pe,weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0623\u062d\u062f_\u0625\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0623\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"D/\u200fM/\u200fYYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0635|\u0645/,isPM:function(e){return"\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635":"\u0645"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u064b\u0627 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0628\u0639\u062f %s",past:"\u0645\u0646\u0630 %s",s:v("s"),ss:v("s"),m:v("m"),mm:v("m"),h:v("h"),hh:v("h"),d:v("d"),dd:v("d"),M:v("M"),MM:v("M"),y:v("y"),yy:v("y")},preparse:function(e){return e.replace(/\u060c/g,",")},postformat:function(e){return e.replace(/\d/g,function(e){return Rt[e]}).replace(/,/g,"\u060c")},week:{dow:6,doy:12}}),M.defineLocale("ar-ma",{months:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648\u0632_\u063a\u0634\u062a_\u0634\u062a\u0646\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0646\u0628\u0631_\u062f\u062c\u0646\u0628\u0631".split("_"),monthsShort:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648\u0632_\u063a\u0634\u062a_\u0634\u062a\u0646\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0646\u0628\u0631_\u062f\u062c\u0646\u0628\u0631".split("_"),weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0627\u062d\u062f_\u0627\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u0627 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0641\u064a %s",past:"\u0645\u0646\u0630 %s",s:"\u062b\u0648\u0627\u0646",ss:"%d \u062b\u0627\u0646\u064a\u0629",m:"\u062f\u0642\u064a\u0642\u0629",mm:"%d \u062f\u0642\u0627\u0626\u0642",h:"\u0633\u0627\u0639\u0629",hh:"%d \u0633\u0627\u0639\u0627\u062a",d:"\u064a\u0648\u0645",dd:"%d \u0623\u064a\u0627\u0645",M:"\u0634\u0647\u0631",MM:"%d \u0623\u0634\u0647\u0631",y:"\u0633\u0646\u0629",yy:"%d \u0633\u0646\u0648\u0627\u062a"},week:{dow:1,doy:4}}),{1:"\u0661",2:"\u0662",3:"\u0663",4:"\u0664",5:"\u0665",6:"\u0666",7:"\u0667",8:"\u0668",9:"\u0669",0:"\u0660"}),Ut={"\u0661":"1","\u0662":"2","\u0663":"3","\u0664":"4","\u0665":"5","\u0666":"6","\u0667":"7","\u0668":"8","\u0669":"9","\u0660":"0"},Gt=(M.defineLocale("ar-sa",{months:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a\u0648_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648_\u0623\u063a\u0633\u0637\u0633_\u0633\u0628\u062a\u0645\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0641\u0645\u0628\u0631_\u062f\u064a\u0633\u0645\u0628\u0631".split("_"),monthsShort:"\u064a\u0646\u0627\u064a\u0631_\u0641\u0628\u0631\u0627\u064a\u0631_\u0645\u0627\u0631\u0633_\u0623\u0628\u0631\u064a\u0644_\u0645\u0627\u064a\u0648_\u064a\u0648\u0646\u064a\u0648_\u064a\u0648\u0644\u064a\u0648_\u0623\u063a\u0633\u0637\u0633_\u0633\u0628\u062a\u0645\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0641\u0645\u0628\u0631_\u062f\u064a\u0633\u0645\u0628\u0631".split("_"),weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0623\u062d\u062f_\u0625\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0623\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0635|\u0645/,isPM:function(e){return"\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635":"\u0645"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u0627 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0641\u064a %s",past:"\u0645\u0646\u0630 %s",s:"\u062b\u0648\u0627\u0646",ss:"%d \u062b\u0627\u0646\u064a\u0629",m:"\u062f\u0642\u064a\u0642\u0629",mm:"%d \u062f\u0642\u0627\u0626\u0642",h:"\u0633\u0627\u0639\u0629",hh:"%d \u0633\u0627\u0639\u0627\u062a",d:"\u064a\u0648\u0645",dd:"%d \u0623\u064a\u0627\u0645",M:"\u0634\u0647\u0631",MM:"%d \u0623\u0634\u0647\u0631",y:"\u0633\u0646\u0629",yy:"%d \u0633\u0646\u0648\u0627\u062a"},preparse:function(e){return e.replace(/[\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669\u0660]/g,function(e){return Ut[e]}).replace(/\u060c/g,",")},postformat:function(e){return e.replace(/\d/g,function(e){return It[e]}).replace(/,/g,"\u060c")},week:{dow:0,doy:6}}),M.defineLocale("ar-tn",{months:"\u062c\u0627\u0646\u0641\u064a_\u0641\u064a\u0641\u0631\u064a_\u0645\u0627\u0631\u0633_\u0623\u0641\u0631\u064a\u0644_\u0645\u0627\u064a_\u062c\u0648\u0627\u0646_\u062c\u0648\u064a\u0644\u064a\u0629_\u0623\u0648\u062a_\u0633\u0628\u062a\u0645\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0641\u0645\u0628\u0631_\u062f\u064a\u0633\u0645\u0628\u0631".split("_"),monthsShort:"\u062c\u0627\u0646\u0641\u064a_\u0641\u064a\u0641\u0631\u064a_\u0645\u0627\u0631\u0633_\u0623\u0641\u0631\u064a\u0644_\u0645\u0627\u064a_\u062c\u0648\u0627\u0646_\u062c\u0648\u064a\u0644\u064a\u0629_\u0623\u0648\u062a_\u0633\u0628\u062a\u0645\u0628\u0631_\u0623\u0643\u062a\u0648\u0628\u0631_\u0646\u0648\u0641\u0645\u0628\u0631_\u062f\u064a\u0633\u0645\u0628\u0631".split("_"),weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0623\u062d\u062f_\u0625\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0623\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u0627 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0644\u0649 \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0641\u064a %s",past:"\u0645\u0646\u0630 %s",s:"\u062b\u0648\u0627\u0646",ss:"%d \u062b\u0627\u0646\u064a\u0629",m:"\u062f\u0642\u064a\u0642\u0629",mm:"%d \u062f\u0642\u0627\u0626\u0642",h:"\u0633\u0627\u0639\u0629",hh:"%d \u0633\u0627\u0639\u0627\u062a",d:"\u064a\u0648\u0645",dd:"%d \u0623\u064a\u0627\u0645",M:"\u0634\u0647\u0631",MM:"%d \u0623\u0634\u0647\u0631",y:"\u0633\u0646\u0629",yy:"%d \u0633\u0646\u0648\u0627\u062a"},week:{dow:1,doy:4}}),{1:"\u0661",2:"\u0662",3:"\u0663",4:"\u0664",5:"\u0665",6:"\u0666",7:"\u0667",8:"\u0668",9:"\u0669",0:"\u0660"}),Vt={"\u0661":"1","\u0662":"2","\u0663":"3","\u0664":"4","\u0665":"5","\u0666":"6","\u0667":"7","\u0668":"8","\u0669":"9","\u0660":"0"},Bt={s:["\u0623\u0642\u0644 \u0645\u0646 \u062b\u0627\u0646\u064a\u0629","\u062b\u0627\u0646\u064a\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062b\u0627\u0646\u064a\u062a\u0627\u0646","\u062b\u0627\u0646\u064a\u062a\u064a\u0646"],"%d \u062b\u0648\u0627\u0646","%d \u062b\u0627\u0646\u064a\u0629","%d \u062b\u0627\u0646\u064a\u0629"],m:["\u0623\u0642\u0644 \u0645\u0646 \u062f\u0642\u064a\u0642\u0629","\u062f\u0642\u064a\u0642\u0629 \u0648\u0627\u062d\u062f\u0629",["\u062f\u0642\u064a\u0642\u062a\u0627\u0646","\u062f\u0642\u064a\u0642\u062a\u064a\u0646"],"%d \u062f\u0642\u0627\u0626\u0642","%d \u062f\u0642\u064a\u0642\u0629","%d \u062f\u0642\u064a\u0642\u0629"],h:["\u0623\u0642\u0644 \u0645\u0646 \u0633\u0627\u0639\u0629","\u0633\u0627\u0639\u0629 \u0648\u0627\u062d\u062f\u0629",["\u0633\u0627\u0639\u062a\u0627\u0646","\u0633\u0627\u0639\u062a\u064a\u0646"],"%d \u0633\u0627\u0639\u0627\u062a","%d \u0633\u0627\u0639\u0629","%d \u0633\u0627\u0639\u0629"],d:["\u0623\u0642\u0644 \u0645\u0646 \u064a\u0648\u0645","\u064a\u0648\u0645 \u0648\u0627\u062d\u062f",["\u064a\u0648\u0645\u0627\u0646","\u064a\u0648\u0645\u064a\u0646"],"%d \u0623\u064a\u0627\u0645","%d \u064a\u0648\u0645\u064b\u0627","%d \u064a\u0648\u0645"],M:["\u0623\u0642\u0644 \u0645\u0646 \u0634\u0647\u0631","\u0634\u0647\u0631 \u0648\u0627\u062d\u062f",["\u0634\u0647\u0631\u0627\u0646","\u0634\u0647\u0631\u064a\u0646"],"%d \u0623\u0634\u0647\u0631","%d \u0634\u0647\u0631\u0627","%d \u0634\u0647\u0631"],y:["\u0623\u0642\u0644 \u0645\u0646 \u0639\u0627\u0645","\u0639\u0627\u0645 \u0648\u0627\u062d\u062f",["\u0639\u0627\u0645\u0627\u0646","\u0639\u0627\u0645\u064a\u0646"],"%d \u0623\u0639\u0648\u0627\u0645","%d \u0639\u0627\u0645\u064b\u0627","%d \u0639\u0627\u0645"]},Ne=["\u064a\u0646\u0627\u064a\u0631","\u0641\u0628\u0631\u0627\u064a\u0631","\u0645\u0627\u0631\u0633","\u0623\u0628\u0631\u064a\u0644","\u0645\u0627\u064a\u0648","\u064a\u0648\u0646\u064a\u0648","\u064a\u0648\u0644\u064a\u0648","\u0623\u063a\u0633\u0637\u0633","\u0633\u0628\u062a\u0645\u0628\u0631","\u0623\u0643\u062a\u0648\u0628\u0631","\u0646\u0648\u0641\u0645\u0628\u0631","\u062f\u064a\u0633\u0645\u0628\u0631"],Kt=(M.defineLocale("ar",{months:Ne,monthsShort:Ne,weekdays:"\u0627\u0644\u0623\u062d\u062f_\u0627\u0644\u0625\u062b\u0646\u064a\u0646_\u0627\u0644\u062b\u0644\u0627\u062b\u0627\u0621_\u0627\u0644\u0623\u0631\u0628\u0639\u0627\u0621_\u0627\u0644\u062e\u0645\u064a\u0633_\u0627\u0644\u062c\u0645\u0639\u0629_\u0627\u0644\u0633\u0628\u062a".split("_"),weekdaysShort:"\u0623\u062d\u062f_\u0625\u062b\u0646\u064a\u0646_\u062b\u0644\u0627\u062b\u0627\u0621_\u0623\u0631\u0628\u0639\u0627\u0621_\u062e\u0645\u064a\u0633_\u062c\u0645\u0639\u0629_\u0633\u0628\u062a".split("_"),weekdaysMin:"\u062d_\u0646_\u062b_\u0631_\u062e_\u062c_\u0633".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"D/\u200fM/\u200fYYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0635|\u0645/,isPM:function(e){return"\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635":"\u0645"},calendar:{sameDay:"[\u0627\u0644\u064a\u0648\u0645 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextDay:"[\u063a\u062f\u064b\u0627 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",nextWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastDay:"[\u0623\u0645\u0633 \u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",lastWeek:"dddd [\u0639\u0646\u062f \u0627\u0644\u0633\u0627\u0639\u0629] LT",sameElse:"L"},relativeTime:{future:"\u0628\u0639\u062f %s",past:"\u0645\u0646\u0630 %s",s:j("s"),ss:j("s"),m:j("m"),mm:j("m"),h:j("h"),hh:j("h"),d:j("d"),dd:j("d"),M:j("M"),MM:j("M"),y:j("y"),yy:j("y")},preparse:function(e){return e.replace(/[\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669\u0660]/g,function(e){return Vt[e]}).replace(/\u060c/g,",")},postformat:function(e){return e.replace(/\d/g,function(e){return Gt[e]}).replace(/,/g,"\u060c")},week:{dow:6,doy:12}}),{1:"-inci",5:"-inci",8:"-inci",70:"-inci",80:"-inci",2:"-nci",7:"-nci",20:"-nci",50:"-nci",3:"-\xfcnc\xfc",4:"-\xfcnc\xfc",100:"-\xfcnc\xfc",6:"-nc\u0131",9:"-uncu",10:"-uncu",30:"-uncu",60:"-\u0131nc\u0131",90:"-\u0131nc\u0131"});function qt(e,a,t){return"m"===t?a?"\u0445\u0432\u0456\u043b\u0456\u043d\u0430":"\u0445\u0432\u0456\u043b\u0456\u043d\u0443":"h"===t?a?"\u0433\u0430\u0434\u0437\u0456\u043d\u0430":"\u0433\u0430\u0434\u0437\u0456\u043d\u0443":e+" "+(e=+e,a=(a={ss:a?"\u0441\u0435\u043a\u0443\u043d\u0434\u0430_\u0441\u0435\u043a\u0443\u043d\u0434\u044b_\u0441\u0435\u043a\u0443\u043d\u0434":"\u0441\u0435\u043a\u0443\u043d\u0434\u0443_\u0441\u0435\u043a\u0443\u043d\u0434\u044b_\u0441\u0435\u043a\u0443\u043d\u0434",mm:a?"\u0445\u0432\u0456\u043b\u0456\u043d\u0430_\u0445\u0432\u0456\u043b\u0456\u043d\u044b_\u0445\u0432\u0456\u043b\u0456\u043d":"\u0445\u0432\u0456\u043b\u0456\u043d\u0443_\u0445\u0432\u0456\u043b\u0456\u043d\u044b_\u0445\u0432\u0456\u043b\u0456\u043d",hh:a?"\u0433\u0430\u0434\u0437\u0456\u043d\u0430_\u0433\u0430\u0434\u0437\u0456\u043d\u044b_\u0433\u0430\u0434\u0437\u0456\u043d":"\u0433\u0430\u0434\u0437\u0456\u043d\u0443_\u0433\u0430\u0434\u0437\u0456\u043d\u044b_\u0433\u0430\u0434\u0437\u0456\u043d",dd:"\u0434\u0437\u0435\u043d\u044c_\u0434\u043d\u0456_\u0434\u0437\u0451\u043d",MM:"\u043c\u0435\u0441\u044f\u0446_\u043c\u0435\u0441\u044f\u0446\u044b_\u043c\u0435\u0441\u044f\u0446\u0430\u045e",yy:"\u0433\u043e\u0434_\u0433\u0430\u0434\u044b_\u0433\u0430\u0434\u043e\u045e"}[t]).split("_"),e%10==1&&e%100!=11?a[0]:2<=e%10&&e%10<=4&&(e%100<10||20<=e%100)?a[1]:a[2])}M.defineLocale("az",{months:"yanvar_fevral_mart_aprel_may_iyun_iyul_avqust_sentyabr_oktyabr_noyabr_dekabr".split("_"),monthsShort:"yan_fev_mar_apr_may_iyn_iyl_avq_sen_okt_noy_dek".split("_"),weekdays:"Bazar_Bazar ert\u0259si_\xc7\u0259r\u015f\u0259nb\u0259 ax\u015fam\u0131_\xc7\u0259r\u015f\u0259nb\u0259_C\xfcm\u0259 ax\u015fam\u0131_C\xfcm\u0259_\u015e\u0259nb\u0259".split("_"),weekdaysShort:"Baz_BzE_\xc7Ax_\xc7\u0259r_CAx_C\xfcm_\u015e\u0259n".split("_"),weekdaysMin:"Bz_BE_\xc7A_\xc7\u0259_CA_C\xfc_\u015e\u0259".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[bug\xfcn saat] LT",nextDay:"[sabah saat] LT",nextWeek:"[g\u0259l\u0259n h\u0259ft\u0259] dddd [saat] LT",lastDay:"[d\xfcn\u0259n] LT",lastWeek:"[ke\xe7\u0259n h\u0259ft\u0259] dddd [saat] LT",sameElse:"L"},relativeTime:{future:"%s sonra",past:"%s \u0259vv\u0259l",s:"bir ne\xe7\u0259 saniy\u0259",ss:"%d saniy\u0259",m:"bir d\u0259qiq\u0259",mm:"%d d\u0259qiq\u0259",h:"bir saat",hh:"%d saat",d:"bir g\xfcn",dd:"%d g\xfcn",M:"bir ay",MM:"%d ay",y:"bir il",yy:"%d il"},meridiemParse:/gec\u0259|s\u0259h\u0259r|g\xfcnd\xfcz|ax\u015fam/,isPM:function(e){return/^(g\xfcnd\xfcz|ax\u015fam)$/.test(e)},meridiem:function(e,a,t){return e<4?"gec\u0259":e<12?"s\u0259h\u0259r":e<17?"g\xfcnd\xfcz":"ax\u015fam"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0131nc\u0131|inci|nci|\xfcnc\xfc|nc\u0131|uncu)/,ordinal:function(e){if(0===e)return e+"-\u0131nc\u0131";var a=e%10;return e+(Kt[a]||Kt[e%100-a]||Kt[100<=e?100:null])},week:{dow:1,doy:7}}),M.defineLocale("be",{months:{format:"\u0441\u0442\u0443\u0434\u0437\u0435\u043d\u044f_\u043b\u044e\u0442\u0430\u0433\u0430_\u0441\u0430\u043a\u0430\u0432\u0456\u043a\u0430_\u043a\u0440\u0430\u0441\u0430\u0432\u0456\u043a\u0430_\u0442\u0440\u0430\u045e\u043d\u044f_\u0447\u044d\u0440\u0432\u0435\u043d\u044f_\u043b\u0456\u043f\u0435\u043d\u044f_\u0436\u043d\u0456\u045e\u043d\u044f_\u0432\u0435\u0440\u0430\u0441\u043d\u044f_\u043a\u0430\u0441\u0442\u0440\u044b\u0447\u043d\u0456\u043a\u0430_\u043b\u0456\u0441\u0442\u0430\u043f\u0430\u0434\u0430_\u0441\u043d\u0435\u0436\u043d\u044f".split("_"),standalone:"\u0441\u0442\u0443\u0434\u0437\u0435\u043d\u044c_\u043b\u044e\u0442\u044b_\u0441\u0430\u043a\u0430\u0432\u0456\u043a_\u043a\u0440\u0430\u0441\u0430\u0432\u0456\u043a_\u0442\u0440\u0430\u0432\u0435\u043d\u044c_\u0447\u044d\u0440\u0432\u0435\u043d\u044c_\u043b\u0456\u043f\u0435\u043d\u044c_\u0436\u043d\u0456\u0432\u0435\u043d\u044c_\u0432\u0435\u0440\u0430\u0441\u0435\u043d\u044c_\u043a\u0430\u0441\u0442\u0440\u044b\u0447\u043d\u0456\u043a_\u043b\u0456\u0441\u0442\u0430\u043f\u0430\u0434_\u0441\u043d\u0435\u0436\u0430\u043d\u044c".split("_")},monthsShort:"\u0441\u0442\u0443\u0434_\u043b\u044e\u0442_\u0441\u0430\u043a_\u043a\u0440\u0430\u0441_\u0442\u0440\u0430\u0432_\u0447\u044d\u0440\u0432_\u043b\u0456\u043f_\u0436\u043d\u0456\u0432_\u0432\u0435\u0440_\u043a\u0430\u0441\u0442_\u043b\u0456\u0441\u0442_\u0441\u043d\u0435\u0436".split("_"),weekdays:{format:"\u043d\u044f\u0434\u0437\u0435\u043b\u044e_\u043f\u0430\u043d\u044f\u0434\u0437\u0435\u043b\u0430\u043a_\u0430\u045e\u0442\u043e\u0440\u0430\u043a_\u0441\u0435\u0440\u0430\u0434\u0443_\u0447\u0430\u0446\u0432\u0435\u0440_\u043f\u044f\u0442\u043d\u0456\u0446\u0443_\u0441\u0443\u0431\u043e\u0442\u0443".split("_"),standalone:"\u043d\u044f\u0434\u0437\u0435\u043b\u044f_\u043f\u0430\u043d\u044f\u0434\u0437\u0435\u043b\u0430\u043a_\u0430\u045e\u0442\u043e\u0440\u0430\u043a_\u0441\u0435\u0440\u0430\u0434\u0430_\u0447\u0430\u0446\u0432\u0435\u0440_\u043f\u044f\u0442\u043d\u0456\u0446\u0430_\u0441\u0443\u0431\u043e\u0442\u0430".split("_"),isFormat:/\[ ?[\u0423\u0443\u045e] ?(?:\u043c\u0456\u043d\u0443\u043b\u0443\u044e|\u043d\u0430\u0441\u0442\u0443\u043f\u043d\u0443\u044e)? ?\] ?dddd/},weekdaysShort:"\u043d\u0434_\u043f\u043d_\u0430\u0442_\u0441\u0440_\u0447\u0446_\u043f\u0442_\u0441\u0431".split("_"),weekdaysMin:"\u043d\u0434_\u043f\u043d_\u0430\u0442_\u0441\u0440_\u0447\u0446_\u043f\u0442_\u0441\u0431".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY \u0433.",LLL:"D MMMM YYYY \u0433., HH:mm",LLLL:"dddd, D MMMM YYYY \u0433., HH:mm"},calendar:{sameDay:"[\u0421\u0451\u043d\u043d\u044f \u045e] LT",nextDay:"[\u0417\u0430\u045e\u0442\u0440\u0430 \u045e] LT",lastDay:"[\u0423\u0447\u043e\u0440\u0430 \u045e] LT",nextWeek:function(){return"[\u0423] dddd [\u045e] LT"},lastWeek:function(){switch(this.day()){case 0:case 3:case 5:case 6:return"[\u0423 \u043c\u0456\u043d\u0443\u043b\u0443\u044e] dddd [\u045e] LT";case 1:case 2:case 4:return"[\u0423 \u043c\u0456\u043d\u0443\u043b\u044b] dddd [\u045e] LT"}},sameElse:"L"},relativeTime:{future:"\u043f\u0440\u0430\u0437 %s",past:"%s \u0442\u0430\u043c\u0443",s:"\u043d\u0435\u043a\u0430\u043b\u044c\u043a\u0456 \u0441\u0435\u043a\u0443\u043d\u0434",m:qt,mm:qt,h:qt,hh:qt,d:"\u0434\u0437\u0435\u043d\u044c",dd:qt,M:"\u043c\u0435\u0441\u044f\u0446",MM:qt,y:"\u0433\u043e\u0434",yy:qt},meridiemParse:/\u043d\u043e\u0447\u044b|\u0440\u0430\u043d\u0456\u0446\u044b|\u0434\u043d\u044f|\u0432\u0435\u0447\u0430\u0440\u0430/,isPM:function(e){return/^(\u0434\u043d\u044f|\u0432\u0435\u0447\u0430\u0440\u0430)$/.test(e)},meridiem:function(e,a,t){return e<4?"\u043d\u043e\u0447\u044b":e<12?"\u0440\u0430\u043d\u0456\u0446\u044b":e<17?"\u0434\u043d\u044f":"\u0432\u0435\u0447\u0430\u0440\u0430"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0456|\u044b|\u0433\u0430)/,ordinal:function(e,a){switch(a){case"M":case"d":case"DDD":case"w":case"W":return e%10!=2&&e%10!=3||e%100==12||e%100==13?e+"-\u044b":e+"-\u0456";case"D":return e+"-\u0433\u0430";default:return e}},week:{dow:1,doy:7}}),M.defineLocale("bg",{months:"\u044f\u043d\u0443\u0430\u0440\u0438_\u0444\u0435\u0432\u0440\u0443\u0430\u0440\u0438_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0438\u043b_\u043c\u0430\u0439_\u044e\u043d\u0438_\u044e\u043b\u0438_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043f\u0442\u0435\u043c\u0432\u0440\u0438_\u043e\u043a\u0442\u043e\u043c\u0432\u0440\u0438_\u043d\u043e\u0435\u043c\u0432\u0440\u0438_\u0434\u0435\u043a\u0435\u043c\u0432\u0440\u0438".split("_"),monthsShort:"\u044f\u043d\u0443_\u0444\u0435\u0432_\u043c\u0430\u0440_\u0430\u043f\u0440_\u043c\u0430\u0439_\u044e\u043d\u0438_\u044e\u043b\u0438_\u0430\u0432\u0433_\u0441\u0435\u043f_\u043e\u043a\u0442_\u043d\u043e\u0435_\u0434\u0435\u043a".split("_"),weekdays:"\u043d\u0435\u0434\u0435\u043b\u044f_\u043f\u043e\u043d\u0435\u0434\u0435\u043b\u043d\u0438\u043a_\u0432\u0442\u043e\u0440\u043d\u0438\u043a_\u0441\u0440\u044f\u0434\u0430_\u0447\u0435\u0442\u0432\u044a\u0440\u0442\u044a\u043a_\u043f\u0435\u0442\u044a\u043a_\u0441\u044a\u0431\u043e\u0442\u0430".split("_"),weekdaysShort:"\u043d\u0435\u0434_\u043f\u043e\u043d_\u0432\u0442\u043e_\u0441\u0440\u044f_\u0447\u0435\u0442_\u043f\u0435\u0442_\u0441\u044a\u0431".split("_"),weekdaysMin:"\u043d\u0434_\u043f\u043d_\u0432\u0442_\u0441\u0440_\u0447\u0442_\u043f\u0442_\u0441\u0431".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"D.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY H:mm",LLLL:"dddd, D MMMM YYYY H:mm"},calendar:{sameDay:"[\u0414\u043d\u0435\u0441 \u0432] LT",nextDay:"[\u0423\u0442\u0440\u0435 \u0432] LT",nextWeek:"dddd [\u0432] LT",lastDay:"[\u0412\u0447\u0435\u0440\u0430 \u0432] LT",lastWeek:function(){switch(this.day()){case 0:case 3:case 6:return"[\u041c\u0438\u043d\u0430\u043b\u0430\u0442\u0430] dddd [\u0432] LT";case 1:case 2:case 4:case 5:return"[\u041c\u0438\u043d\u0430\u043b\u0438\u044f] dddd [\u0432] LT"}},sameElse:"L"},relativeTime:{future:"\u0441\u043b\u0435\u0434 %s",past:"\u043f\u0440\u0435\u0434\u0438 %s",s:"\u043d\u044f\u043a\u043e\u043b\u043a\u043e \u0441\u0435\u043a\u0443\u043d\u0434\u0438",ss:"%d \u0441\u0435\u043a\u0443\u043d\u0434\u0438",m:"\u043c\u0438\u043d\u0443\u0442\u0430",mm:"%d \u043c\u0438\u043d\u0443\u0442\u0438",h:"\u0447\u0430\u0441",hh:"%d \u0447\u0430\u0441\u0430",d:"\u0434\u0435\u043d",dd:"%d \u0434\u0435\u043d\u0430",w:"\u0441\u0435\u0434\u043c\u0438\u0446\u0430",ww:"%d \u0441\u0435\u0434\u043c\u0438\u0446\u0438",M:"\u043c\u0435\u0441\u0435\u0446",MM:"%d \u043c\u0435\u0441\u0435\u0446\u0430",y:"\u0433\u043e\u0434\u0438\u043d\u0430",yy:"%d \u0433\u043e\u0434\u0438\u043d\u0438"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0435\u0432|\u0435\u043d|\u0442\u0438|\u0432\u0438|\u0440\u0438|\u043c\u0438)/,ordinal:function(e){var a=e%10,t=e%100;return 0===e?e+"-\u0435\u0432":0==t?e+"-\u0435\u043d":10<t&&t<20?e+"-\u0442\u0438":1==a?e+"-\u0432\u0438":2==a?e+"-\u0440\u0438":7==a||8==a?e+"-\u043c\u0438":e+"-\u0442\u0438"},week:{dow:1,doy:7}}),M.defineLocale("bm",{months:"Zanwuyekalo_Fewuruyekalo_Marisikalo_Awirilikalo_M\u025bkalo_Zuw\u025bnkalo_Zuluyekalo_Utikalo_S\u025btanburukalo_\u0254kut\u0254burukalo_Nowanburukalo_Desanburukalo".split("_"),monthsShort:"Zan_Few_Mar_Awi_M\u025b_Zuw_Zul_Uti_S\u025bt_\u0254ku_Now_Des".split("_"),weekdays:"Kari_Nt\u025bn\u025bn_Tarata_Araba_Alamisa_Juma_Sibiri".split("_"),weekdaysShort:"Kar_Nt\u025b_Tar_Ara_Ala_Jum_Sib".split("_"),weekdaysMin:"Ka_Nt_Ta_Ar_Al_Ju_Si".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"MMMM [tile] D [san] YYYY",LLL:"MMMM [tile] D [san] YYYY [l\u025br\u025b] HH:mm",LLLL:"dddd MMMM [tile] D [san] YYYY [l\u025br\u025b] HH:mm"},calendar:{sameDay:"[Bi l\u025br\u025b] LT",nextDay:"[Sini l\u025br\u025b] LT",nextWeek:"dddd [don l\u025br\u025b] LT",lastDay:"[Kunu l\u025br\u025b] LT",lastWeek:"dddd [t\u025bm\u025bnen l\u025br\u025b] LT",sameElse:"L"},relativeTime:{future:"%s k\u0254n\u0254",past:"a b\u025b %s b\u0254",s:"sanga dama dama",ss:"sekondi %d",m:"miniti kelen",mm:"miniti %d",h:"l\u025br\u025b kelen",hh:"l\u025br\u025b %d",d:"tile kelen",dd:"tile %d",M:"kalo kelen",MM:"kalo %d",y:"san kelen",yy:"san %d"},week:{dow:1,doy:4}});var Zt={1:"\u09e7",2:"\u09e8",3:"\u09e9",4:"\u09ea",5:"\u09eb",6:"\u09ec",7:"\u09ed",8:"\u09ee",9:"\u09ef",0:"\u09e6"},$t={"\u09e7":"1","\u09e8":"2","\u09e9":"3","\u09ea":"4","\u09eb":"5","\u09ec":"6","\u09ed":"7","\u09ee":"8","\u09ef":"9","\u09e6":"0"},Qt=(M.defineLocale("bn-bd",{months:"\u099c\u09be\u09a8\u09c1\u09df\u09be\u09b0\u09bf_\u09ab\u09c7\u09ac\u09cd\u09b0\u09c1\u09df\u09be\u09b0\u09bf_\u09ae\u09be\u09b0\u09cd\u099a_\u098f\u09aa\u09cd\u09b0\u09bf\u09b2_\u09ae\u09c7_\u099c\u09c1\u09a8_\u099c\u09c1\u09b2\u09be\u0987_\u0986\u0997\u09b8\u09cd\u099f_\u09b8\u09c7\u09aa\u09cd\u099f\u09c7\u09ae\u09cd\u09ac\u09b0_\u0985\u0995\u09cd\u099f\u09cb\u09ac\u09b0_\u09a8\u09ad\u09c7\u09ae\u09cd\u09ac\u09b0_\u09a1\u09bf\u09b8\u09c7\u09ae\u09cd\u09ac\u09b0".split("_"),monthsShort:"\u099c\u09be\u09a8\u09c1_\u09ab\u09c7\u09ac\u09cd\u09b0\u09c1_\u09ae\u09be\u09b0\u09cd\u099a_\u098f\u09aa\u09cd\u09b0\u09bf\u09b2_\u09ae\u09c7_\u099c\u09c1\u09a8_\u099c\u09c1\u09b2\u09be\u0987_\u0986\u0997\u09b8\u09cd\u099f_\u09b8\u09c7\u09aa\u09cd\u099f_\u0985\u0995\u09cd\u099f\u09cb_\u09a8\u09ad\u09c7_\u09a1\u09bf\u09b8\u09c7".split("_"),weekdays:"\u09b0\u09ac\u09bf\u09ac\u09be\u09b0_\u09b8\u09cb\u09ae\u09ac\u09be\u09b0_\u09ae\u0999\u09cd\u0997\u09b2\u09ac\u09be\u09b0_\u09ac\u09c1\u09a7\u09ac\u09be\u09b0_\u09ac\u09c3\u09b9\u09b8\u09cd\u09aa\u09a4\u09bf\u09ac\u09be\u09b0_\u09b6\u09c1\u0995\u09cd\u09b0\u09ac\u09be\u09b0_\u09b6\u09a8\u09bf\u09ac\u09be\u09b0".split("_"),weekdaysShort:"\u09b0\u09ac\u09bf_\u09b8\u09cb\u09ae_\u09ae\u0999\u09cd\u0997\u09b2_\u09ac\u09c1\u09a7_\u09ac\u09c3\u09b9\u09b8\u09cd\u09aa\u09a4\u09bf_\u09b6\u09c1\u0995\u09cd\u09b0_\u09b6\u09a8\u09bf".split("_"),weekdaysMin:"\u09b0\u09ac\u09bf_\u09b8\u09cb\u09ae_\u09ae\u0999\u09cd\u0997\u09b2_\u09ac\u09c1\u09a7_\u09ac\u09c3\u09b9_\u09b6\u09c1\u0995\u09cd\u09b0_\u09b6\u09a8\u09bf".split("_"),longDateFormat:{LT:"A h:mm \u09b8\u09ae\u09df",LTS:"A h:mm:ss \u09b8\u09ae\u09df",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u09b8\u09ae\u09df",LLLL:"dddd, D MMMM YYYY, A h:mm \u09b8\u09ae\u09df"},calendar:{sameDay:"[\u0986\u099c] LT",nextDay:"[\u0986\u0997\u09be\u09ae\u09c0\u0995\u09be\u09b2] LT",nextWeek:"dddd, LT",lastDay:"[\u0997\u09a4\u0995\u09be\u09b2] LT",lastWeek:"[\u0997\u09a4] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u09aa\u09b0\u09c7",past:"%s \u0986\u0997\u09c7",s:"\u0995\u09df\u09c7\u0995 \u09b8\u09c7\u0995\u09c7\u09a8\u09cd\u09a1",ss:"%d \u09b8\u09c7\u0995\u09c7\u09a8\u09cd\u09a1",m:"\u098f\u0995 \u09ae\u09bf\u09a8\u09bf\u099f",mm:"%d \u09ae\u09bf\u09a8\u09bf\u099f",h:"\u098f\u0995 \u0998\u09a8\u09cd\u099f\u09be",hh:"%d \u0998\u09a8\u09cd\u099f\u09be",d:"\u098f\u0995 \u09a6\u09bf\u09a8",dd:"%d \u09a6\u09bf\u09a8",M:"\u098f\u0995 \u09ae\u09be\u09b8",MM:"%d \u09ae\u09be\u09b8",y:"\u098f\u0995 \u09ac\u099b\u09b0",yy:"%d \u09ac\u099b\u09b0"},preparse:function(e){return e.replace(/[\u09e7\u09e8\u09e9\u09ea\u09eb\u09ec\u09ed\u09ee\u09ef\u09e6]/g,function(e){return $t[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return Zt[e]})},meridiemParse:/\u09b0\u09be\u09a4|\u09ad\u09cb\u09b0|\u09b8\u0995\u09be\u09b2|\u09a6\u09c1\u09aa\u09c1\u09b0|\u09ac\u09bf\u0995\u09be\u09b2|\u09b8\u09a8\u09cd\u09a7\u09cd\u09af\u09be|\u09b0\u09be\u09a4/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u09b0\u09be\u09a4"===a?e<4?e:e+12:"\u09ad\u09cb\u09b0"===a||"\u09b8\u0995\u09be\u09b2"===a?e:"\u09a6\u09c1\u09aa\u09c1\u09b0"===a?3<=e?e:e+12:"\u09ac\u09bf\u0995\u09be\u09b2"===a||"\u09b8\u09a8\u09cd\u09a7\u09cd\u09af\u09be"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u09b0\u09be\u09a4":e<6?"\u09ad\u09cb\u09b0":e<12?"\u09b8\u0995\u09be\u09b2":e<15?"\u09a6\u09c1\u09aa\u09c1\u09b0":e<18?"\u09ac\u09bf\u0995\u09be\u09b2":e<20?"\u09b8\u09a8\u09cd\u09a7\u09cd\u09af\u09be":"\u09b0\u09be\u09a4"},week:{dow:0,doy:6}}),{1:"\u09e7",2:"\u09e8",3:"\u09e9",4:"\u09ea",5:"\u09eb",6:"\u09ec",7:"\u09ed",8:"\u09ee",9:"\u09ef",0:"\u09e6"}),Xt={"\u09e7":"1","\u09e8":"2","\u09e9":"3","\u09ea":"4","\u09eb":"5","\u09ec":"6","\u09ed":"7","\u09ee":"8","\u09ef":"9","\u09e6":"0"},es=(M.defineLocale("bn",{months:"\u099c\u09be\u09a8\u09c1\u09df\u09be\u09b0\u09bf_\u09ab\u09c7\u09ac\u09cd\u09b0\u09c1\u09df\u09be\u09b0\u09bf_\u09ae\u09be\u09b0\u09cd\u099a_\u098f\u09aa\u09cd\u09b0\u09bf\u09b2_\u09ae\u09c7_\u099c\u09c1\u09a8_\u099c\u09c1\u09b2\u09be\u0987_\u0986\u0997\u09b8\u09cd\u099f_\u09b8\u09c7\u09aa\u09cd\u099f\u09c7\u09ae\u09cd\u09ac\u09b0_\u0985\u0995\u09cd\u099f\u09cb\u09ac\u09b0_\u09a8\u09ad\u09c7\u09ae\u09cd\u09ac\u09b0_\u09a1\u09bf\u09b8\u09c7\u09ae\u09cd\u09ac\u09b0".split("_"),monthsShort:"\u099c\u09be\u09a8\u09c1_\u09ab\u09c7\u09ac\u09cd\u09b0\u09c1_\u09ae\u09be\u09b0\u09cd\u099a_\u098f\u09aa\u09cd\u09b0\u09bf\u09b2_\u09ae\u09c7_\u099c\u09c1\u09a8_\u099c\u09c1\u09b2\u09be\u0987_\u0986\u0997\u09b8\u09cd\u099f_\u09b8\u09c7\u09aa\u09cd\u099f_\u0985\u0995\u09cd\u099f\u09cb_\u09a8\u09ad\u09c7_\u09a1\u09bf\u09b8\u09c7".split("_"),weekdays:"\u09b0\u09ac\u09bf\u09ac\u09be\u09b0_\u09b8\u09cb\u09ae\u09ac\u09be\u09b0_\u09ae\u0999\u09cd\u0997\u09b2\u09ac\u09be\u09b0_\u09ac\u09c1\u09a7\u09ac\u09be\u09b0_\u09ac\u09c3\u09b9\u09b8\u09cd\u09aa\u09a4\u09bf\u09ac\u09be\u09b0_\u09b6\u09c1\u0995\u09cd\u09b0\u09ac\u09be\u09b0_\u09b6\u09a8\u09bf\u09ac\u09be\u09b0".split("_"),weekdaysShort:"\u09b0\u09ac\u09bf_\u09b8\u09cb\u09ae_\u09ae\u0999\u09cd\u0997\u09b2_\u09ac\u09c1\u09a7_\u09ac\u09c3\u09b9\u09b8\u09cd\u09aa\u09a4\u09bf_\u09b6\u09c1\u0995\u09cd\u09b0_\u09b6\u09a8\u09bf".split("_"),weekdaysMin:"\u09b0\u09ac\u09bf_\u09b8\u09cb\u09ae_\u09ae\u0999\u09cd\u0997\u09b2_\u09ac\u09c1\u09a7_\u09ac\u09c3\u09b9_\u09b6\u09c1\u0995\u09cd\u09b0_\u09b6\u09a8\u09bf".split("_"),longDateFormat:{LT:"A h:mm \u09b8\u09ae\u09df",LTS:"A h:mm:ss \u09b8\u09ae\u09df",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u09b8\u09ae\u09df",LLLL:"dddd, D MMMM YYYY, A h:mm \u09b8\u09ae\u09df"},calendar:{sameDay:"[\u0986\u099c] LT",nextDay:"[\u0986\u0997\u09be\u09ae\u09c0\u0995\u09be\u09b2] LT",nextWeek:"dddd, LT",lastDay:"[\u0997\u09a4\u0995\u09be\u09b2] LT",lastWeek:"[\u0997\u09a4] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u09aa\u09b0\u09c7",past:"%s \u0986\u0997\u09c7",s:"\u0995\u09df\u09c7\u0995 \u09b8\u09c7\u0995\u09c7\u09a8\u09cd\u09a1",ss:"%d \u09b8\u09c7\u0995\u09c7\u09a8\u09cd\u09a1",m:"\u098f\u0995 \u09ae\u09bf\u09a8\u09bf\u099f",mm:"%d \u09ae\u09bf\u09a8\u09bf\u099f",h:"\u098f\u0995 \u0998\u09a8\u09cd\u099f\u09be",hh:"%d \u0998\u09a8\u09cd\u099f\u09be",d:"\u098f\u0995 \u09a6\u09bf\u09a8",dd:"%d \u09a6\u09bf\u09a8",M:"\u098f\u0995 \u09ae\u09be\u09b8",MM:"%d \u09ae\u09be\u09b8",y:"\u098f\u0995 \u09ac\u099b\u09b0",yy:"%d \u09ac\u099b\u09b0"},preparse:function(e){return e.replace(/[\u09e7\u09e8\u09e9\u09ea\u09eb\u09ec\u09ed\u09ee\u09ef\u09e6]/g,function(e){return Xt[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return Qt[e]})},meridiemParse:/\u09b0\u09be\u09a4|\u09b8\u0995\u09be\u09b2|\u09a6\u09c1\u09aa\u09c1\u09b0|\u09ac\u09bf\u0995\u09be\u09b2|\u09b0\u09be\u09a4/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u09b0\u09be\u09a4"===a&&4<=e||"\u09a6\u09c1\u09aa\u09c1\u09b0"===a&&e<5||"\u09ac\u09bf\u0995\u09be\u09b2"===a?e+12:e},meridiem:function(e,a,t){return e<4?"\u09b0\u09be\u09a4":e<10?"\u09b8\u0995\u09be\u09b2":e<17?"\u09a6\u09c1\u09aa\u09c1\u09b0":e<20?"\u09ac\u09bf\u0995\u09be\u09b2":"\u09b0\u09be\u09a4"},week:{dow:0,doy:6}}),{1:"\u0f21",2:"\u0f22",3:"\u0f23",4:"\u0f24",5:"\u0f25",6:"\u0f26",7:"\u0f27",8:"\u0f28",9:"\u0f29",0:"\u0f20"}),as={"\u0f21":"1","\u0f22":"2","\u0f23":"3","\u0f24":"4","\u0f25":"5","\u0f26":"6","\u0f27":"7","\u0f28":"8","\u0f29":"9","\u0f20":"0"};function ts(e,a,t){return e+" "+(t={mm:"munutenn",MM:"miz",dd:"devezh"}[t],2!==(e=e)?t:void 0!==(e={m:"v",b:"v",d:"z"})[(t=t).charAt(0)]?e[t.charAt(0)]+t.substring(1):t)}M.defineLocale("bo",{months:"\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f51\u0f44\u0f0b\u0f54\u0f7c_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f42\u0f49\u0f72\u0f66\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f42\u0f66\u0f74\u0f58\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f5e\u0f72\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f63\u0f94\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f51\u0fb2\u0f74\u0f42\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f51\u0f74\u0f53\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f62\u0f92\u0fb1\u0f51\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f51\u0f42\u0f74\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f45\u0f74\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f45\u0f74\u0f0b\u0f42\u0f45\u0f72\u0f42\u0f0b\u0f54_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f56\u0f45\u0f74\u0f0b\u0f42\u0f49\u0f72\u0f66\u0f0b\u0f54".split("_"),monthsShort:"\u0f5f\u0fb3\u0f0b1_\u0f5f\u0fb3\u0f0b2_\u0f5f\u0fb3\u0f0b3_\u0f5f\u0fb3\u0f0b4_\u0f5f\u0fb3\u0f0b5_\u0f5f\u0fb3\u0f0b6_\u0f5f\u0fb3\u0f0b7_\u0f5f\u0fb3\u0f0b8_\u0f5f\u0fb3\u0f0b9_\u0f5f\u0fb3\u0f0b10_\u0f5f\u0fb3\u0f0b11_\u0f5f\u0fb3\u0f0b12".split("_"),monthsShortRegex:/^(\u0f5f\u0fb3\u0f0b\d{1,2})/,monthsParseExact:!0,weekdays:"\u0f42\u0f5f\u0f60\u0f0b\u0f49\u0f72\u0f0b\u0f58\u0f0b_\u0f42\u0f5f\u0f60\u0f0b\u0f5f\u0fb3\u0f0b\u0f56\u0f0b_\u0f42\u0f5f\u0f60\u0f0b\u0f58\u0f72\u0f42\u0f0b\u0f51\u0f58\u0f62\u0f0b_\u0f42\u0f5f\u0f60\u0f0b\u0f63\u0fb7\u0f42\u0f0b\u0f54\u0f0b_\u0f42\u0f5f\u0f60\u0f0b\u0f55\u0f74\u0f62\u0f0b\u0f56\u0f74_\u0f42\u0f5f\u0f60\u0f0b\u0f54\u0f0b\u0f66\u0f44\u0f66\u0f0b_\u0f42\u0f5f\u0f60\u0f0b\u0f66\u0fa4\u0f7a\u0f53\u0f0b\u0f54\u0f0b".split("_"),weekdaysShort:"\u0f49\u0f72\u0f0b\u0f58\u0f0b_\u0f5f\u0fb3\u0f0b\u0f56\u0f0b_\u0f58\u0f72\u0f42\u0f0b\u0f51\u0f58\u0f62\u0f0b_\u0f63\u0fb7\u0f42\u0f0b\u0f54\u0f0b_\u0f55\u0f74\u0f62\u0f0b\u0f56\u0f74_\u0f54\u0f0b\u0f66\u0f44\u0f66\u0f0b_\u0f66\u0fa4\u0f7a\u0f53\u0f0b\u0f54\u0f0b".split("_"),weekdaysMin:"\u0f49\u0f72_\u0f5f\u0fb3_\u0f58\u0f72\u0f42_\u0f63\u0fb7\u0f42_\u0f55\u0f74\u0f62_\u0f66\u0f44\u0f66_\u0f66\u0fa4\u0f7a\u0f53".split("_"),longDateFormat:{LT:"A h:mm",LTS:"A h:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm",LLLL:"dddd, D MMMM YYYY, A h:mm"},calendar:{sameDay:"[\u0f51\u0f72\u0f0b\u0f62\u0f72\u0f44] LT",nextDay:"[\u0f66\u0f44\u0f0b\u0f49\u0f72\u0f53] LT",nextWeek:"[\u0f56\u0f51\u0f74\u0f53\u0f0b\u0f55\u0fb2\u0f42\u0f0b\u0f62\u0f97\u0f7a\u0f66\u0f0b\u0f58], LT",lastDay:"[\u0f41\u0f0b\u0f66\u0f44] LT",lastWeek:"[\u0f56\u0f51\u0f74\u0f53\u0f0b\u0f55\u0fb2\u0f42\u0f0b\u0f58\u0f50\u0f60\u0f0b\u0f58] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0f63\u0f0b",past:"%s \u0f66\u0f94\u0f53\u0f0b\u0f63",s:"\u0f63\u0f58\u0f0b\u0f66\u0f44",ss:"%d \u0f66\u0f90\u0f62\u0f0b\u0f46\u0f0d",m:"\u0f66\u0f90\u0f62\u0f0b\u0f58\u0f0b\u0f42\u0f45\u0f72\u0f42",mm:"%d \u0f66\u0f90\u0f62\u0f0b\u0f58",h:"\u0f46\u0f74\u0f0b\u0f5a\u0f7c\u0f51\u0f0b\u0f42\u0f45\u0f72\u0f42",hh:"%d \u0f46\u0f74\u0f0b\u0f5a\u0f7c\u0f51",d:"\u0f49\u0f72\u0f53\u0f0b\u0f42\u0f45\u0f72\u0f42",dd:"%d \u0f49\u0f72\u0f53\u0f0b",M:"\u0f5f\u0fb3\u0f0b\u0f56\u0f0b\u0f42\u0f45\u0f72\u0f42",MM:"%d \u0f5f\u0fb3\u0f0b\u0f56",y:"\u0f63\u0f7c\u0f0b\u0f42\u0f45\u0f72\u0f42",yy:"%d \u0f63\u0f7c"},preparse:function(e){return e.replace(/[\u0f21\u0f22\u0f23\u0f24\u0f25\u0f26\u0f27\u0f28\u0f29\u0f20]/g,function(e){return as[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return es[e]})},meridiemParse:/\u0f58\u0f5a\u0f53\u0f0b\u0f58\u0f7c|\u0f5e\u0f7c\u0f42\u0f66\u0f0b\u0f40\u0f66|\u0f49\u0f72\u0f53\u0f0b\u0f42\u0f74\u0f44|\u0f51\u0f42\u0f7c\u0f44\u0f0b\u0f51\u0f42|\u0f58\u0f5a\u0f53\u0f0b\u0f58\u0f7c/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0f58\u0f5a\u0f53\u0f0b\u0f58\u0f7c"===a&&4<=e||"\u0f49\u0f72\u0f53\u0f0b\u0f42\u0f74\u0f44"===a&&e<5||"\u0f51\u0f42\u0f7c\u0f44\u0f0b\u0f51\u0f42"===a?e+12:e},meridiem:function(e,a,t){return e<4?"\u0f58\u0f5a\u0f53\u0f0b\u0f58\u0f7c":e<10?"\u0f5e\u0f7c\u0f42\u0f66\u0f0b\u0f40\u0f66":e<17?"\u0f49\u0f72\u0f53\u0f0b\u0f42\u0f74\u0f44":e<20?"\u0f51\u0f42\u0f7c\u0f44\u0f0b\u0f51\u0f42":"\u0f58\u0f5a\u0f53\u0f0b\u0f58\u0f7c"},week:{dow:0,doy:6}});L=[/^gen/i,/^c[\u02bc\']hwe/i,/^meu/i,/^ebr/i,/^mae/i,/^(mez|eve)/i,/^gou/i,/^eos/i,/^gwe/i,/^her/i,/^du/i,/^ker/i],c=/^(genver|c[\u02bc\']hwevrer|meurzh|ebrel|mae|mezheven|gouere|eost|gwengolo|here|du|kerzu|gen|c[\u02bc\']hwe|meu|ebr|mae|eve|gou|eos|gwe|her|du|ker)/i,ta=[/^Su/i,/^Lu/i,/^Me([^r]|$)/i,/^Mer/i,/^Ya/i,/^Gw/i,/^Sa/i];function ss(e,a,t){var s=e+" ";switch(t){case"ss":return s+=1===e?"sekunda":2===e||3===e||4===e?"sekunde":"sekundi";case"m":return a?"jedna minuta":"jedne minute";case"mm":return s+=1!==e&&(2===e||3===e||4===e)?"minute":"minuta";case"h":return a?"jedan sat":"jednog sata";case"hh":return s+=1===e?"sat":2===e||3===e||4===e?"sata":"sati";case"dd":return s+=1===e?"dan":"dana";case"MM":return s+=1===e?"mjesec":2===e||3===e||4===e?"mjeseca":"mjeseci";case"yy":return s+=1!==e&&(2===e||3===e||4===e)?"godine":"godina"}}M.defineLocale("br",{months:"Genver_C\u02bchwevrer_Meurzh_Ebrel_Mae_Mezheven_Gouere_Eost_Gwengolo_Here_Du_Kerzu".split("_"),monthsShort:"Gen_C\u02bchwe_Meu_Ebr_Mae_Eve_Gou_Eos_Gwe_Her_Du_Ker".split("_"),weekdays:"Sul_Lun_Meurzh_Merc\u02bcher_Yaou_Gwener_Sadorn".split("_"),weekdaysShort:"Sul_Lun_Meu_Mer_Yao_Gwe_Sad".split("_"),weekdaysMin:"Su_Lu_Me_Mer_Ya_Gw_Sa".split("_"),weekdaysParse:ta,fullWeekdaysParse:[/^sul/i,/^lun/i,/^meurzh/i,/^merc[\u02bc\']her/i,/^yaou/i,/^gwener/i,/^sadorn/i],shortWeekdaysParse:[/^Sul/i,/^Lun/i,/^Meu/i,/^Mer/i,/^Yao/i,/^Gwe/i,/^Sad/i],minWeekdaysParse:ta,monthsRegex:c,monthsShortRegex:c,monthsStrictRegex:/^(genver|c[\u02bc\']hwevrer|meurzh|ebrel|mae|mezheven|gouere|eost|gwengolo|here|du|kerzu)/i,monthsShortStrictRegex:/^(gen|c[\u02bc\']hwe|meu|ebr|mae|eve|gou|eos|gwe|her|du|ker)/i,monthsParse:L,longMonthsParse:L,shortMonthsParse:L,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D [a viz] MMMM YYYY",LLL:"D [a viz] MMMM YYYY HH:mm",LLLL:"dddd, D [a viz] MMMM YYYY HH:mm"},calendar:{sameDay:"[Hiziv da] LT",nextDay:"[Warc\u02bchoazh da] LT",nextWeek:"dddd [da] LT",lastDay:"[Dec\u02bch da] LT",lastWeek:"dddd [paset da] LT",sameElse:"L"},relativeTime:{future:"a-benn %s",past:"%s \u02bczo",s:"un nebeud segondenno\xf9",ss:"%d eilenn",m:"ur vunutenn",mm:ts,h:"un eur",hh:"%d eur",d:"un devezh",dd:ts,M:"ur miz",MM:ts,y:"ur bloaz",yy:function(e){switch(function e(a){if(9<a)return e(a%10);return a}(e)){case 1:case 3:case 4:case 5:case 9:return e+" bloaz";default:return e+" vloaz"}}},dayOfMonthOrdinalParse:/\d{1,2}(a\xf1|vet)/,ordinal:function(e){return e+(1===e?"a\xf1":"vet")},week:{dow:1,doy:4},meridiemParse:/a.m.|g.m./,isPM:function(e){return"g.m."===e},meridiem:function(e,a,t){return e<12?"a.m.":"g.m."}}),M.defineLocale("bs",{months:"januar_februar_mart_april_maj_juni_juli_august_septembar_oktobar_novembar_decembar".split("_"),monthsShort:"jan._feb._mar._apr._maj._jun._jul._aug._sep._okt._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"nedjelja_ponedjeljak_utorak_srijeda_\u010detvrtak_petak_subota".split("_"),weekdaysShort:"ned._pon._uto._sri._\u010det._pet._sub.".split("_"),weekdaysMin:"ne_po_ut_sr_\u010de_pe_su".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd, D. MMMM YYYY H:mm"},calendar:{sameDay:"[danas u] LT",nextDay:"[sutra u] LT",nextWeek:function(){switch(this.day()){case 0:return"[u] [nedjelju] [u] LT";case 3:return"[u] [srijedu] [u] LT";case 6:return"[u] [subotu] [u] LT";case 1:case 2:case 4:case 5:return"[u] dddd [u] LT"}},lastDay:"[ju\u010der u] LT",lastWeek:function(){switch(this.day()){case 0:case 3:return"[pro\u0161lu] dddd [u] LT";case 6:return"[pro\u0161le] [subote] [u] LT";case 1:case 2:case 4:case 5:return"[pro\u0161li] dddd [u] LT"}},sameElse:"L"},relativeTime:{future:"za %s",past:"prije %s",s:"par sekundi",ss:ss,m:ss,mm:ss,h:ss,hh:ss,d:"dan",dd:ss,M:"mjesec",MM:ss,y:"godinu",yy:ss},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}}),M.defineLocale("ca",{months:{standalone:"gener_febrer_mar\xe7_abril_maig_juny_juliol_agost_setembre_octubre_novembre_desembre".split("_"),format:"de gener_de febrer_de mar\xe7_d'abril_de maig_de juny_de juliol_d'agost_de setembre_d'octubre_de novembre_de desembre".split("_"),isFormat:/D[oD]?(\s)+MMMM/},monthsShort:"gen._febr._mar\xe7_abr._maig_juny_jul._ag._set._oct._nov._des.".split("_"),monthsParseExact:!0,weekdays:"diumenge_dilluns_dimarts_dimecres_dijous_divendres_dissabte".split("_"),weekdaysShort:"dg._dl._dt._dc._dj._dv._ds.".split("_"),weekdaysMin:"dg_dl_dt_dc_dj_dv_ds".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM [de] YYYY",ll:"D MMM YYYY",LLL:"D MMMM [de] YYYY [a les] H:mm",lll:"D MMM YYYY, H:mm",LLLL:"dddd D MMMM [de] YYYY [a les] H:mm",llll:"ddd D MMM YYYY, H:mm"},calendar:{sameDay:function(){return"[avui a "+(1!==this.hours()?"les":"la")+"] LT"},nextDay:function(){return"[dem\xe0 a "+(1!==this.hours()?"les":"la")+"] LT"},nextWeek:function(){return"dddd [a "+(1!==this.hours()?"les":"la")+"] LT"},lastDay:function(){return"[ahir a "+(1!==this.hours()?"les":"la")+"] LT"},lastWeek:function(){return"[el] dddd [passat a "+(1!==this.hours()?"les":"la")+"] LT"},sameElse:"L"},relativeTime:{future:"d'aqu\xed %s",past:"fa %s",s:"uns segons",ss:"%d segons",m:"un minut",mm:"%d minuts",h:"una hora",hh:"%d hores",d:"un dia",dd:"%d dies",M:"un mes",MM:"%d mesos",y:"un any",yy:"%d anys"},dayOfMonthOrdinalParse:/\d{1,2}(r|n|t|\xe8|a)/,ordinal:function(e,a){return e+("w"!==a&&"W"!==a?1===e?"r":2===e?"n":3===e?"r":4===e?"t":"\xe8":"a")},week:{dow:1,doy:4}});f={format:"leden_\xfanor_b\u0159ezen_duben_kv\u011bten_\u010derven_\u010dervenec_srpen_z\xe1\u0159\xed_\u0159\xedjen_listopad_prosinec".split("_"),standalone:"ledna_\xfanora_b\u0159ezna_dubna_kv\u011btna_\u010dervna_\u010dervence_srpna_z\xe1\u0159\xed_\u0159\xedjna_listopadu_prosince".split("_")},ke="led_\xfano_b\u0159e_dub_kv\u011b_\u010dvn_\u010dvc_srp_z\xe1\u0159_\u0159\xedj_lis_pro".split("_"),y=[/^led/i,/^\xfano/i,/^b\u0159e/i,/^dub/i,/^kv\u011b/i,/^(\u010dvn|\u010derven$|\u010dervna)/i,/^(\u010dvc|\u010dervenec|\u010dervence)/i,/^srp/i,/^z\xe1\u0159/i,/^\u0159\xedj/i,/^lis/i,/^pro/i],_=/^(leden|\xfanor|b\u0159ezen|duben|kv\u011bten|\u010dervenec|\u010dervence|\u010derven|\u010dervna|srpen|z\xe1\u0159\xed|\u0159\xedjen|listopad|prosinec|led|\xfano|b\u0159e|dub|kv\u011b|\u010dvn|\u010dvc|srp|z\xe1\u0159|\u0159\xedj|lis|pro)/i;function ns(e){return 1<e&&e<5&&1!=~~(e/10)}function P(e,a,t,s){var n=e+" ";switch(t){case"s":return a||s?"p\xe1r sekund":"p\xe1r sekundami";case"ss":return a||s?n+(ns(e)?"sekundy":"sekund"):n+"sekundami";case"m":return a?"minuta":s?"minutu":"minutou";case"mm":return a||s?n+(ns(e)?"minuty":"minut"):n+"minutami";case"h":return a?"hodina":s?"hodinu":"hodinou";case"hh":return a||s?n+(ns(e)?"hodiny":"hodin"):n+"hodinami";case"d":return a||s?"den":"dnem";case"dd":return a||s?n+(ns(e)?"dny":"dn\xed"):n+"dny";case"M":return a||s?"m\u011bs\xedc":"m\u011bs\xedcem";case"MM":return a||s?n+(ns(e)?"m\u011bs\xedce":"m\u011bs\xedc\u016f"):n+"m\u011bs\xedci";case"y":return a||s?"rok":"rokem";case"yy":return a||s?n+(ns(e)?"roky":"let"):n+"lety"}}function rs(e,a,t,s){e={m:["eine Minute","einer Minute"],h:["eine Stunde","einer Stunde"],d:["ein Tag","einem Tag"],dd:[e+" Tage",e+" Tagen"],w:["eine Woche","einer Woche"],M:["ein Monat","einem Monat"],MM:[e+" Monate",e+" Monaten"],y:["ein Jahr","einem Jahr"],yy:[e+" Jahre",e+" Jahren"]};return a?e[t][0]:e[t][1]}function ds(e,a,t,s){e={m:["eine Minute","einer Minute"],h:["eine Stunde","einer Stunde"],d:["ein Tag","einem Tag"],dd:[e+" Tage",e+" Tagen"],w:["eine Woche","einer Woche"],M:["ein Monat","einem Monat"],MM:[e+" Monate",e+" Monaten"],y:["ein Jahr","einem Jahr"],yy:[e+" Jahre",e+" Jahren"]};return a?e[t][0]:e[t][1]}function is(e,a,t,s){e={m:["eine Minute","einer Minute"],h:["eine Stunde","einer Stunde"],d:["ein Tag","einem Tag"],dd:[e+" Tage",e+" Tagen"],w:["eine Woche","einer Woche"],M:["ein Monat","einem Monat"],MM:[e+" Monate",e+" Monaten"],y:["ein Jahr","einem Jahr"],yy:[e+" Jahre",e+" Jahren"]};return a?e[t][0]:e[t][1]}M.defineLocale("cs",{months:f,monthsShort:ke,monthsRegex:_,monthsShortRegex:_,monthsStrictRegex:/^(leden|ledna|\xfanora|\xfanor|b\u0159ezen|b\u0159ezna|duben|dubna|kv\u011bten|kv\u011btna|\u010dervenec|\u010dervence|\u010derven|\u010dervna|srpen|srpna|z\xe1\u0159\xed|\u0159\xedjen|\u0159\xedjna|listopadu|listopad|prosinec|prosince)/i,monthsShortStrictRegex:/^(led|\xfano|b\u0159e|dub|kv\u011b|\u010dvn|\u010dvc|srp|z\xe1\u0159|\u0159\xedj|lis|pro)/i,monthsParse:y,longMonthsParse:y,shortMonthsParse:y,weekdays:"ned\u011ble_pond\u011bl\xed_\xfater\xfd_st\u0159eda_\u010dtvrtek_p\xe1tek_sobota".split("_"),weekdaysShort:"ne_po_\xfat_st_\u010dt_p\xe1_so".split("_"),weekdaysMin:"ne_po_\xfat_st_\u010dt_p\xe1_so".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd D. MMMM YYYY H:mm",l:"D. M. YYYY"},calendar:{sameDay:"[dnes v] LT",nextDay:"[z\xedtra v] LT",nextWeek:function(){switch(this.day()){case 0:return"[v ned\u011bli v] LT";case 1:case 2:return"[v] dddd [v] LT";case 3:return"[ve st\u0159edu v] LT";case 4:return"[ve \u010dtvrtek v] LT";case 5:return"[v p\xe1tek v] LT";case 6:return"[v sobotu v] LT"}},lastDay:"[v\u010dera v] LT",lastWeek:function(){switch(this.day()){case 0:return"[minulou ned\u011bli v] LT";case 1:case 2:return"[minul\xe9] dddd [v] LT";case 3:return"[minulou st\u0159edu v] LT";case 4:case 5:return"[minul\xfd] dddd [v] LT";case 6:return"[minulou sobotu v] LT"}},sameElse:"L"},relativeTime:{future:"za %s",past:"p\u0159ed %s",s:P,ss:P,m:P,mm:P,h:P,hh:P,d:P,dd:P,M:P,MM:P,y:P,yy:P},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("cv",{months:"\u043a\u04d1\u0440\u043b\u0430\u0447_\u043d\u0430\u0440\u04d1\u0441_\u043f\u0443\u0448_\u0430\u043a\u0430_\u043c\u0430\u0439_\u04ab\u04d7\u0440\u0442\u043c\u0435_\u0443\u0442\u04d1_\u04ab\u0443\u0440\u043b\u0430_\u0430\u0432\u04d1\u043d_\u044e\u043f\u0430_\u0447\u04f3\u043a_\u0440\u0430\u0448\u0442\u0430\u0432".split("_"),monthsShort:"\u043a\u04d1\u0440_\u043d\u0430\u0440_\u043f\u0443\u0448_\u0430\u043a\u0430_\u043c\u0430\u0439_\u04ab\u04d7\u0440_\u0443\u0442\u04d1_\u04ab\u0443\u0440_\u0430\u0432\u043d_\u044e\u043f\u0430_\u0447\u04f3\u043a_\u0440\u0430\u0448".split("_"),weekdays:"\u0432\u044b\u0440\u0441\u0430\u0440\u043d\u0438\u043a\u0443\u043d_\u0442\u0443\u043d\u0442\u0438\u043a\u0443\u043d_\u044b\u0442\u043b\u0430\u0440\u0438\u043a\u0443\u043d_\u044e\u043d\u043a\u0443\u043d_\u043a\u04d7\u04ab\u043d\u0435\u0440\u043d\u0438\u043a\u0443\u043d_\u044d\u0440\u043d\u0435\u043a\u0443\u043d_\u0448\u04d1\u043c\u0430\u0442\u043a\u0443\u043d".split("_"),weekdaysShort:"\u0432\u044b\u0440_\u0442\u0443\u043d_\u044b\u0442\u043b_\u044e\u043d_\u043a\u04d7\u04ab_\u044d\u0440\u043d_\u0448\u04d1\u043c".split("_"),weekdaysMin:"\u0432\u0440_\u0442\u043d_\u044b\u0442_\u044e\u043d_\u043a\u04ab_\u044d\u0440_\u0448\u043c".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD-MM-YYYY",LL:"YYYY [\u04ab\u0443\u043b\u0445\u0438] MMMM [\u0443\u0439\u04d1\u0445\u04d7\u043d] D[-\u043c\u04d7\u0448\u04d7]",LLL:"YYYY [\u04ab\u0443\u043b\u0445\u0438] MMMM [\u0443\u0439\u04d1\u0445\u04d7\u043d] D[-\u043c\u04d7\u0448\u04d7], HH:mm",LLLL:"dddd, YYYY [\u04ab\u0443\u043b\u0445\u0438] MMMM [\u0443\u0439\u04d1\u0445\u04d7\u043d] D[-\u043c\u04d7\u0448\u04d7], HH:mm"},calendar:{sameDay:"[\u041f\u0430\u044f\u043d] LT [\u0441\u0435\u0445\u0435\u0442\u0440\u0435]",nextDay:"[\u042b\u0440\u0430\u043d] LT [\u0441\u0435\u0445\u0435\u0442\u0440\u0435]",lastDay:"[\u04d6\u043d\u0435\u0440] LT [\u0441\u0435\u0445\u0435\u0442\u0440\u0435]",nextWeek:"[\u04aa\u0438\u0442\u0435\u0441] dddd LT [\u0441\u0435\u0445\u0435\u0442\u0440\u0435]",lastWeek:"[\u0418\u0440\u0442\u043d\u04d7] dddd LT [\u0441\u0435\u0445\u0435\u0442\u0440\u0435]",sameElse:"L"},relativeTime:{future:function(e){return e+(/\u0441\u0435\u0445\u0435\u0442$/i.exec(e)?"\u0440\u0435\u043d":/\u04ab\u0443\u043b$/i.exec(e)?"\u0442\u0430\u043d":"\u0440\u0430\u043d")},past:"%s \u043a\u0430\u044f\u043b\u043b\u0430",s:"\u043f\u04d7\u0440-\u0438\u043a \u04ab\u0435\u043a\u043a\u0443\u043d\u0442",ss:"%d \u04ab\u0435\u043a\u043a\u0443\u043d\u0442",m:"\u043f\u04d7\u0440 \u043c\u0438\u043d\u0443\u0442",mm:"%d \u043c\u0438\u043d\u0443\u0442",h:"\u043f\u04d7\u0440 \u0441\u0435\u0445\u0435\u0442",hh:"%d \u0441\u0435\u0445\u0435\u0442",d:"\u043f\u04d7\u0440 \u043a\u0443\u043d",dd:"%d \u043a\u0443\u043d",M:"\u043f\u04d7\u0440 \u0443\u0439\u04d1\u0445",MM:"%d \u0443\u0439\u04d1\u0445",y:"\u043f\u04d7\u0440 \u04ab\u0443\u043b",yy:"%d \u04ab\u0443\u043b"},dayOfMonthOrdinalParse:/\d{1,2}-\u043c\u04d7\u0448/,ordinal:"%d-\u043c\u04d7\u0448",week:{dow:1,doy:7}}),M.defineLocale("cy",{months:"Ionawr_Chwefror_Mawrth_Ebrill_Mai_Mehefin_Gorffennaf_Awst_Medi_Hydref_Tachwedd_Rhagfyr".split("_"),monthsShort:"Ion_Chwe_Maw_Ebr_Mai_Meh_Gor_Aws_Med_Hyd_Tach_Rhag".split("_"),weekdays:"Dydd Sul_Dydd Llun_Dydd Mawrth_Dydd Mercher_Dydd Iau_Dydd Gwener_Dydd Sadwrn".split("_"),weekdaysShort:"Sul_Llun_Maw_Mer_Iau_Gwe_Sad".split("_"),weekdaysMin:"Su_Ll_Ma_Me_Ia_Gw_Sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Heddiw am] LT",nextDay:"[Yfory am] LT",nextWeek:"dddd [am] LT",lastDay:"[Ddoe am] LT",lastWeek:"dddd [diwethaf am] LT",sameElse:"L"},relativeTime:{future:"mewn %s",past:"%s yn \xf4l",s:"ychydig eiliadau",ss:"%d eiliad",m:"munud",mm:"%d munud",h:"awr",hh:"%d awr",d:"diwrnod",dd:"%d diwrnod",M:"mis",MM:"%d mis",y:"blwyddyn",yy:"%d flynedd"},dayOfMonthOrdinalParse:/\d{1,2}(fed|ain|af|il|ydd|ed|eg)/,ordinal:function(e){var a="";return 20<e?a=40===e||50===e||60===e||80===e||100===e?"fed":"ain":0<e&&(a=["","af","il","ydd","ydd","ed","ed","ed","fed","fed","fed","eg","fed","eg","eg","fed","eg","eg","fed","eg","fed"][e]),e+a},week:{dow:1,doy:4}}),M.defineLocale("da",{months:"januar_februar_marts_april_maj_juni_juli_august_september_oktober_november_december".split("_"),monthsShort:"jan_feb_mar_apr_maj_jun_jul_aug_sep_okt_nov_dec".split("_"),weekdays:"s\xf8ndag_mandag_tirsdag_onsdag_torsdag_fredag_l\xf8rdag".split("_"),weekdaysShort:"s\xf8n_man_tir_ons_tor_fre_l\xf8r".split("_"),weekdaysMin:"s\xf8_ma_ti_on_to_fr_l\xf8".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY HH:mm",LLLL:"dddd [d.] D. MMMM YYYY [kl.] HH:mm"},calendar:{sameDay:"[i dag kl.] LT",nextDay:"[i morgen kl.] LT",nextWeek:"p\xe5 dddd [kl.] LT",lastDay:"[i g\xe5r kl.] LT",lastWeek:"[i] dddd[s kl.] LT",sameElse:"L"},relativeTime:{future:"om %s",past:"%s siden",s:"f\xe5 sekunder",ss:"%d sekunder",m:"et minut",mm:"%d minutter",h:"en time",hh:"%d timer",d:"en dag",dd:"%d dage",M:"en m\xe5ned",MM:"%d m\xe5neder",y:"et \xe5r",yy:"%d \xe5r"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("de-at",{months:"J\xe4nner_Februar_M\xe4rz_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"),monthsShort:"J\xe4n._Feb._M\xe4rz_Apr._Mai_Juni_Juli_Aug._Sep._Okt._Nov._Dez.".split("_"),monthsParseExact:!0,weekdays:"Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"),weekdaysShort:"So._Mo._Di._Mi._Do._Fr._Sa.".split("_"),weekdaysMin:"So_Mo_Di_Mi_Do_Fr_Sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY HH:mm",LLLL:"dddd, D. MMMM YYYY HH:mm"},calendar:{sameDay:"[heute um] LT [Uhr]",sameElse:"L",nextDay:"[morgen um] LT [Uhr]",nextWeek:"dddd [um] LT [Uhr]",lastDay:"[gestern um] LT [Uhr]",lastWeek:"[letzten] dddd [um] LT [Uhr]"},relativeTime:{future:"in %s",past:"vor %s",s:"ein paar Sekunden",ss:"%d Sekunden",m:rs,mm:"%d Minuten",h:rs,hh:"%d Stunden",d:rs,dd:rs,w:rs,ww:"%d Wochen",M:rs,MM:rs,y:rs,yy:rs},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("de-ch",{months:"Januar_Februar_M\xe4rz_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"),monthsShort:"Jan._Feb._M\xe4rz_Apr._Mai_Juni_Juli_Aug._Sep._Okt._Nov._Dez.".split("_"),monthsParseExact:!0,weekdays:"Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"),weekdaysShort:"So_Mo_Di_Mi_Do_Fr_Sa".split("_"),weekdaysMin:"So_Mo_Di_Mi_Do_Fr_Sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY HH:mm",LLLL:"dddd, D. MMMM YYYY HH:mm"},calendar:{sameDay:"[heute um] LT [Uhr]",sameElse:"L",nextDay:"[morgen um] LT [Uhr]",nextWeek:"dddd [um] LT [Uhr]",lastDay:"[gestern um] LT [Uhr]",lastWeek:"[letzten] dddd [um] LT [Uhr]"},relativeTime:{future:"in %s",past:"vor %s",s:"ein paar Sekunden",ss:"%d Sekunden",m:ds,mm:"%d Minuten",h:ds,hh:"%d Stunden",d:ds,dd:ds,w:ds,ww:"%d Wochen",M:ds,MM:ds,y:ds,yy:ds},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("de",{months:"Januar_Februar_M\xe4rz_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember".split("_"),monthsShort:"Jan._Feb._M\xe4rz_Apr._Mai_Juni_Juli_Aug._Sep._Okt._Nov._Dez.".split("_"),monthsParseExact:!0,weekdays:"Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag".split("_"),weekdaysShort:"So._Mo._Di._Mi._Do._Fr._Sa.".split("_"),weekdaysMin:"So_Mo_Di_Mi_Do_Fr_Sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY HH:mm",LLLL:"dddd, D. MMMM YYYY HH:mm"},calendar:{sameDay:"[heute um] LT [Uhr]",sameElse:"L",nextDay:"[morgen um] LT [Uhr]",nextWeek:"dddd [um] LT [Uhr]",lastDay:"[gestern um] LT [Uhr]",lastWeek:"[letzten] dddd [um] LT [Uhr]"},relativeTime:{future:"in %s",past:"vor %s",s:"ein paar Sekunden",ss:"%d Sekunden",m:is,mm:"%d Minuten",h:is,hh:"%d Stunden",d:is,dd:is,w:is,ww:"%d Wochen",M:is,MM:is,y:is,yy:is},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}});Y=["\u0796\u07ac\u0782\u07aa\u0787\u07a6\u0783\u07a9","\u078a\u07ac\u0784\u07b0\u0783\u07aa\u0787\u07a6\u0783\u07a9","\u0789\u07a7\u0783\u07a8\u0797\u07aa","\u0787\u07ad\u0795\u07b0\u0783\u07a9\u078d\u07aa","\u0789\u07ad","\u0796\u07ab\u0782\u07b0","\u0796\u07aa\u078d\u07a6\u0787\u07a8","\u0787\u07af\u078e\u07a6\u0790\u07b0\u0793\u07aa","\u0790\u07ac\u0795\u07b0\u0793\u07ac\u0789\u07b0\u0784\u07a6\u0783\u07aa","\u0787\u07ae\u0786\u07b0\u0793\u07af\u0784\u07a6\u0783\u07aa","\u0782\u07ae\u0788\u07ac\u0789\u07b0\u0784\u07a6\u0783\u07aa","\u0791\u07a8\u0790\u07ac\u0789\u07b0\u0784\u07a6\u0783\u07aa"],Ke=["\u0787\u07a7\u078b\u07a8\u0787\u07b0\u078c\u07a6","\u0780\u07af\u0789\u07a6","\u0787\u07a6\u0782\u07b0\u078e\u07a7\u0783\u07a6","\u0784\u07aa\u078b\u07a6","\u0784\u07aa\u0783\u07a7\u0790\u07b0\u078a\u07a6\u078c\u07a8","\u0780\u07aa\u0786\u07aa\u0783\u07aa","\u0780\u07ae\u0782\u07a8\u0780\u07a8\u0783\u07aa"];M.defineLocale("dv",{months:Y,monthsShort:Y,weekdays:Ke,weekdaysShort:Ke,weekdaysMin:"\u0787\u07a7\u078b\u07a8_\u0780\u07af\u0789\u07a6_\u0787\u07a6\u0782\u07b0_\u0784\u07aa\u078b\u07a6_\u0784\u07aa\u0783\u07a7_\u0780\u07aa\u0786\u07aa_\u0780\u07ae\u0782\u07a8".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"D/M/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0789\u0786|\u0789\u078a/,isPM:function(e){return"\u0789\u078a"===e},meridiem:function(e,a,t){return e<12?"\u0789\u0786":"\u0789\u078a"},calendar:{sameDay:"[\u0789\u07a8\u0787\u07a6\u078b\u07aa] LT",nextDay:"[\u0789\u07a7\u078b\u07a6\u0789\u07a7] LT",nextWeek:"dddd LT",lastDay:"[\u0787\u07a8\u0787\u07b0\u0794\u07ac] LT",lastWeek:"[\u078a\u07a7\u0787\u07a8\u078c\u07aa\u0788\u07a8] dddd LT",sameElse:"L"},relativeTime:{future:"\u078c\u07ac\u0783\u07ad\u078e\u07a6\u0787\u07a8 %s",past:"\u0786\u07aa\u0783\u07a8\u0782\u07b0 %s",s:"\u0790\u07a8\u0786\u07aa\u0782\u07b0\u078c\u07aa\u0786\u07ae\u0785\u07ac\u0787\u07b0",ss:"d% \u0790\u07a8\u0786\u07aa\u0782\u07b0\u078c\u07aa",m:"\u0789\u07a8\u0782\u07a8\u0793\u07ac\u0787\u07b0",mm:"\u0789\u07a8\u0782\u07a8\u0793\u07aa %d",h:"\u078e\u07a6\u0791\u07a8\u0787\u07a8\u0783\u07ac\u0787\u07b0",hh:"\u078e\u07a6\u0791\u07a8\u0787\u07a8\u0783\u07aa %d",d:"\u078b\u07aa\u0788\u07a6\u0780\u07ac\u0787\u07b0",dd:"\u078b\u07aa\u0788\u07a6\u0790\u07b0 %d",M:"\u0789\u07a6\u0780\u07ac\u0787\u07b0",MM:"\u0789\u07a6\u0790\u07b0 %d",y:"\u0787\u07a6\u0780\u07a6\u0783\u07ac\u0787\u07b0",yy:"\u0787\u07a6\u0780\u07a6\u0783\u07aa %d"},preparse:function(e){return e.replace(/\u060c/g,",")},postformat:function(e){return e.replace(/,/g,"\u060c")},week:{dow:7,doy:12}}),M.defineLocale("el",{monthsNominativeEl:"\u0399\u03b1\u03bd\u03bf\u03c5\u03ac\u03c1\u03b9\u03bf\u03c2_\u03a6\u03b5\u03b2\u03c1\u03bf\u03c5\u03ac\u03c1\u03b9\u03bf\u03c2_\u039c\u03ac\u03c1\u03c4\u03b9\u03bf\u03c2_\u0391\u03c0\u03c1\u03af\u03bb\u03b9\u03bf\u03c2_\u039c\u03ac\u03b9\u03bf\u03c2_\u0399\u03bf\u03cd\u03bd\u03b9\u03bf\u03c2_\u0399\u03bf\u03cd\u03bb\u03b9\u03bf\u03c2_\u0391\u03cd\u03b3\u03bf\u03c5\u03c3\u03c4\u03bf\u03c2_\u03a3\u03b5\u03c0\u03c4\u03ad\u03bc\u03b2\u03c1\u03b9\u03bf\u03c2_\u039f\u03ba\u03c4\u03ce\u03b2\u03c1\u03b9\u03bf\u03c2_\u039d\u03bf\u03ad\u03bc\u03b2\u03c1\u03b9\u03bf\u03c2_\u0394\u03b5\u03ba\u03ad\u03bc\u03b2\u03c1\u03b9\u03bf\u03c2".split("_"),monthsGenitiveEl:"\u0399\u03b1\u03bd\u03bf\u03c5\u03b1\u03c1\u03af\u03bf\u03c5_\u03a6\u03b5\u03b2\u03c1\u03bf\u03c5\u03b1\u03c1\u03af\u03bf\u03c5_\u039c\u03b1\u03c1\u03c4\u03af\u03bf\u03c5_\u0391\u03c0\u03c1\u03b9\u03bb\u03af\u03bf\u03c5_\u039c\u03b1\u0390\u03bf\u03c5_\u0399\u03bf\u03c5\u03bd\u03af\u03bf\u03c5_\u0399\u03bf\u03c5\u03bb\u03af\u03bf\u03c5_\u0391\u03c5\u03b3\u03bf\u03cd\u03c3\u03c4\u03bf\u03c5_\u03a3\u03b5\u03c0\u03c4\u03b5\u03bc\u03b2\u03c1\u03af\u03bf\u03c5_\u039f\u03ba\u03c4\u03c9\u03b2\u03c1\u03af\u03bf\u03c5_\u039d\u03bf\u03b5\u03bc\u03b2\u03c1\u03af\u03bf\u03c5_\u0394\u03b5\u03ba\u03b5\u03bc\u03b2\u03c1\u03af\u03bf\u03c5".split("_"),months:function(e,a){return e?("string"==typeof a&&/D/.test(a.substring(0,a.indexOf("MMMM")))?this._monthsGenitiveEl:this._monthsNominativeEl)[e.month()]:this._monthsNominativeEl},monthsShort:"\u0399\u03b1\u03bd_\u03a6\u03b5\u03b2_\u039c\u03b1\u03c1_\u0391\u03c0\u03c1_\u039c\u03b1\u03ca_\u0399\u03bf\u03c5\u03bd_\u0399\u03bf\u03c5\u03bb_\u0391\u03c5\u03b3_\u03a3\u03b5\u03c0_\u039f\u03ba\u03c4_\u039d\u03bf\u03b5_\u0394\u03b5\u03ba".split("_"),weekdays:"\u039a\u03c5\u03c1\u03b9\u03b1\u03ba\u03ae_\u0394\u03b5\u03c5\u03c4\u03ad\u03c1\u03b1_\u03a4\u03c1\u03af\u03c4\u03b7_\u03a4\u03b5\u03c4\u03ac\u03c1\u03c4\u03b7_\u03a0\u03ad\u03bc\u03c0\u03c4\u03b7_\u03a0\u03b1\u03c1\u03b1\u03c3\u03ba\u03b5\u03c5\u03ae_\u03a3\u03ac\u03b2\u03b2\u03b1\u03c4\u03bf".split("_"),weekdaysShort:"\u039a\u03c5\u03c1_\u0394\u03b5\u03c5_\u03a4\u03c1\u03b9_\u03a4\u03b5\u03c4_\u03a0\u03b5\u03bc_\u03a0\u03b1\u03c1_\u03a3\u03b1\u03b2".split("_"),weekdaysMin:"\u039a\u03c5_\u0394\u03b5_\u03a4\u03c1_\u03a4\u03b5_\u03a0\u03b5_\u03a0\u03b1_\u03a3\u03b1".split("_"),meridiem:function(e,a,t){return 11<e?t?"\u03bc\u03bc":"\u039c\u039c":t?"\u03c0\u03bc":"\u03a0\u039c"},isPM:function(e){return"\u03bc"===(e+"").toLowerCase()[0]},meridiemParse:/[\u03a0\u039c]\.?\u039c?\.?/i,longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendarEl:{sameDay:"[\u03a3\u03ae\u03bc\u03b5\u03c1\u03b1 {}] LT",nextDay:"[\u0391\u03cd\u03c1\u03b9\u03bf {}] LT",nextWeek:"dddd [{}] LT",lastDay:"[\u03a7\u03b8\u03b5\u03c2 {}] LT",lastWeek:function(){switch(this.day()){case 6:return"[\u03c4\u03bf \u03c0\u03c1\u03bf\u03b7\u03b3\u03bf\u03cd\u03bc\u03b5\u03bd\u03bf] dddd [{}] LT";default:return"[\u03c4\u03b7\u03bd \u03c0\u03c1\u03bf\u03b7\u03b3\u03bf\u03cd\u03bc\u03b5\u03bd\u03b7] dddd [{}] LT"}},sameElse:"L"},calendar:function(e,a){var t,e=this._calendarEl[e],s=a&&a.hours();return t=e,(e="undefined"!=typeof Function&&t instanceof Function||"[object Function]"===Object.prototype.toString.call(t)?e.apply(a):e).replace("{}",s%12==1?"\u03c3\u03c4\u03b7":"\u03c3\u03c4\u03b9\u03c2")},relativeTime:{future:"\u03c3\u03b5 %s",past:"%s \u03c0\u03c1\u03b9\u03bd",s:"\u03bb\u03af\u03b3\u03b1 \u03b4\u03b5\u03c5\u03c4\u03b5\u03c1\u03cc\u03bb\u03b5\u03c0\u03c4\u03b1",ss:"%d \u03b4\u03b5\u03c5\u03c4\u03b5\u03c1\u03cc\u03bb\u03b5\u03c0\u03c4\u03b1",m:"\u03ad\u03bd\u03b1 \u03bb\u03b5\u03c0\u03c4\u03cc",mm:"%d \u03bb\u03b5\u03c0\u03c4\u03ac",h:"\u03bc\u03af\u03b1 \u03ce\u03c1\u03b1",hh:"%d \u03ce\u03c1\u03b5\u03c2",d:"\u03bc\u03af\u03b1 \u03bc\u03ad\u03c1\u03b1",dd:"%d \u03bc\u03ad\u03c1\u03b5\u03c2",M:"\u03ad\u03bd\u03b1\u03c2 \u03bc\u03ae\u03bd\u03b1\u03c2",MM:"%d \u03bc\u03ae\u03bd\u03b5\u03c2",y:"\u03ad\u03bd\u03b1\u03c2 \u03c7\u03c1\u03cc\u03bd\u03bf\u03c2",yy:"%d \u03c7\u03c1\u03cc\u03bd\u03b9\u03b1"},dayOfMonthOrdinalParse:/\d{1,2}\u03b7/,ordinal:"%d\u03b7",week:{dow:1,doy:4}}),M.defineLocale("en-au",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:0,doy:4}}),M.defineLocale("en-ca",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"YYYY-MM-DD",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY h:mm A",LLLL:"dddd, MMMM D, YYYY h:mm A"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")}}),M.defineLocale("en-gb",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),M.defineLocale("en-ie",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),M.defineLocale("en-il",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")}}),M.defineLocale("en-in",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:0,doy:6}}),M.defineLocale("en-nz",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),M.defineLocale("en-sg",{months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),monthsShort:"Jan_Feb_Mar_Apr_May_Jun_Jul_Aug_Sep_Oct_Nov_Dec".split("_"),weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),weekdaysShort:"Sun_Mon_Tue_Wed_Thu_Fri_Sat".split("_"),weekdaysMin:"Su_Mo_Tu_We_Th_Fr_Sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Today at] LT",nextDay:"[Tomorrow at] LT",nextWeek:"dddd [at] LT",lastDay:"[Yesterday at] LT",lastWeek:"[Last] dddd [at] LT",sameElse:"L"},relativeTime:{future:"in %s",past:"%s ago",s:"a few seconds",ss:"%d seconds",m:"a minute",mm:"%d minutes",h:"an hour",hh:"%d hours",d:"a day",dd:"%d days",M:"a month",MM:"%d months",y:"a year",yy:"%d years"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),M.defineLocale("eo",{months:"januaro_februaro_marto_aprilo_majo_junio_julio_a\u016dgusto_septembro_oktobro_novembro_decembro".split("_"),monthsShort:"jan_feb_mart_apr_maj_jun_jul_a\u016dg_sept_okt_nov_dec".split("_"),weekdays:"diman\u0109o_lundo_mardo_merkredo_\u0135a\u016ddo_vendredo_sabato".split("_"),weekdaysShort:"dim_lun_mard_merk_\u0135a\u016d_ven_sab".split("_"),weekdaysMin:"di_lu_ma_me_\u0135a_ve_sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"[la] D[-an de] MMMM, YYYY",LLL:"[la] D[-an de] MMMM, YYYY HH:mm",LLLL:"dddd[n], [la] D[-an de] MMMM, YYYY HH:mm",llll:"ddd, [la] D[-an de] MMM, YYYY HH:mm"},meridiemParse:/[ap]\.t\.m/i,isPM:function(e){return"p"===e.charAt(0).toLowerCase()},meridiem:function(e,a,t){return 11<e?t?"p.t.m.":"P.T.M.":t?"a.t.m.":"A.T.M."},calendar:{sameDay:"[Hodia\u016d je] LT",nextDay:"[Morga\u016d je] LT",nextWeek:"dddd[n je] LT",lastDay:"[Hiera\u016d je] LT",lastWeek:"[pasintan] dddd[n je] LT",sameElse:"L"},relativeTime:{future:"post %s",past:"anta\u016d %s",s:"kelkaj sekundoj",ss:"%d sekundoj",m:"unu minuto",mm:"%d minutoj",h:"unu horo",hh:"%d horoj",d:"unu tago",dd:"%d tagoj",M:"unu monato",MM:"%d monatoj",y:"unu jaro",yy:"%d jaroj"},dayOfMonthOrdinalParse:/\d{1,2}a/,ordinal:"%da",week:{dow:1,doy:7}});var _s="ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_"),os="ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"),r=[/^ene/i,/^feb/i,/^mar/i,/^abr/i,/^may/i,/^jun/i,/^jul/i,/^ago/i,/^sep/i,/^oct/i,/^nov/i,/^dic/i],Ht=/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,ms=(M.defineLocale("es-do",{months:"enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?os:_s)[e.month()]:_s},monthsRegex:Ht,monthsShortRegex:Ht,monthsStrictRegex:/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i,monthsShortStrictRegex:/^(ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,monthsParse:r,longMonthsParse:r,shortMonthsParse:r,weekdays:"domingo_lunes_martes_mi\xe9rcoles_jueves_viernes_s\xe1bado".split("_"),weekdaysShort:"dom._lun._mar._mi\xe9._jue._vie._s\xe1b.".split("_"),weekdaysMin:"do_lu_ma_mi_ju_vi_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY h:mm A",LLLL:"dddd, D [de] MMMM [de] YYYY h:mm A"},calendar:{sameDay:function(){return"[hoy a la"+(1!==this.hours()?"s":"")+"] LT"},nextDay:function(){return"[ma\xf1ana a la"+(1!==this.hours()?"s":"")+"] LT"},nextWeek:function(){return"dddd [a la"+(1!==this.hours()?"s":"")+"] LT"},lastDay:function(){return"[ayer a la"+(1!==this.hours()?"s":"")+"] LT"},lastWeek:function(){return"[el] dddd [pasado a la"+(1!==this.hours()?"s":"")+"] LT"},sameElse:"L"},relativeTime:{future:"en %s",past:"hace %s",s:"unos segundos",ss:"%d segundos",m:"un minuto",mm:"%d minutos",h:"una hora",hh:"%d horas",d:"un d\xeda",dd:"%d d\xedas",w:"una semana",ww:"%d semanas",M:"un mes",MM:"%d meses",y:"un a\xf1o",yy:"%d a\xf1os"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),"ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_")),us="ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"),vt=[/^ene/i,/^feb/i,/^mar/i,/^abr/i,/^may/i,/^jun/i,/^jul/i,/^ago/i,/^sep/i,/^oct/i,/^nov/i,/^dic/i],jt=/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,ls=(M.defineLocale("es-mx",{months:"enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?us:ms)[e.month()]:ms},monthsRegex:jt,monthsShortRegex:jt,monthsStrictRegex:/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i,monthsShortStrictRegex:/^(ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,monthsParse:vt,longMonthsParse:vt,shortMonthsParse:vt,weekdays:"domingo_lunes_martes_mi\xe9rcoles_jueves_viernes_s\xe1bado".split("_"),weekdaysShort:"dom._lun._mar._mi\xe9._jue._vie._s\xe1b.".split("_"),weekdaysMin:"do_lu_ma_mi_ju_vi_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY H:mm",LLLL:"dddd, D [de] MMMM [de] YYYY H:mm"},calendar:{sameDay:function(){return"[hoy a la"+(1!==this.hours()?"s":"")+"] LT"},nextDay:function(){return"[ma\xf1ana a la"+(1!==this.hours()?"s":"")+"] LT"},nextWeek:function(){return"dddd [a la"+(1!==this.hours()?"s":"")+"] LT"},lastDay:function(){return"[ayer a la"+(1!==this.hours()?"s":"")+"] LT"},lastWeek:function(){return"[el] dddd [pasado a la"+(1!==this.hours()?"s":"")+"] LT"},sameElse:"L"},relativeTime:{future:"en %s",past:"hace %s",s:"unos segundos",ss:"%d segundos",m:"un minuto",mm:"%d minutos",h:"una hora",hh:"%d horas",d:"un d\xeda",dd:"%d d\xedas",w:"una semana",ww:"%d semanas",M:"un mes",MM:"%d meses",y:"un a\xf1o",yy:"%d a\xf1os"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:0,doy:4},invalidDate:"Fecha inv\xe1lida"}),"ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_")),Ms="ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"),x=[/^ene/i,/^feb/i,/^mar/i,/^abr/i,/^may/i,/^jun/i,/^jul/i,/^ago/i,/^sep/i,/^oct/i,/^nov/i,/^dic/i],na=/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,hs=(M.defineLocale("es-us",{months:"enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?Ms:ls)[e.month()]:ls},monthsRegex:na,monthsShortRegex:na,monthsStrictRegex:/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i,monthsShortStrictRegex:/^(ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,monthsParse:x,longMonthsParse:x,shortMonthsParse:x,weekdays:"domingo_lunes_martes_mi\xe9rcoles_jueves_viernes_s\xe1bado".split("_"),weekdaysShort:"dom._lun._mar._mi\xe9._jue._vie._s\xe1b.".split("_"),weekdaysMin:"do_lu_ma_mi_ju_vi_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"MM/DD/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY h:mm A",LLLL:"dddd, D [de] MMMM [de] YYYY h:mm A"},calendar:{sameDay:function(){return"[hoy a la"+(1!==this.hours()?"s":"")+"] LT"},nextDay:function(){return"[ma\xf1ana a la"+(1!==this.hours()?"s":"")+"] LT"},nextWeek:function(){return"dddd [a la"+(1!==this.hours()?"s":"")+"] LT"},lastDay:function(){return"[ayer a la"+(1!==this.hours()?"s":"")+"] LT"},lastWeek:function(){return"[el] dddd [pasado a la"+(1!==this.hours()?"s":"")+"] LT"},sameElse:"L"},relativeTime:{future:"en %s",past:"hace %s",s:"unos segundos",ss:"%d segundos",m:"un minuto",mm:"%d minutos",h:"una hora",hh:"%d horas",d:"un d\xeda",dd:"%d d\xedas",w:"una semana",ww:"%d semanas",M:"un mes",MM:"%d meses",y:"un a\xf1o",yy:"%d a\xf1os"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:0,doy:6}}),"ene._feb._mar._abr._may._jun._jul._ago._sep._oct._nov._dic.".split("_")),cs="ene_feb_mar_abr_may_jun_jul_ago_sep_oct_nov_dic".split("_"),ge=[/^ene/i,/^feb/i,/^mar/i,/^abr/i,/^may/i,/^jun/i,/^jul/i,/^ago/i,/^sep/i,/^oct/i,/^nov/i,/^dic/i],n=/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i;function Ls(e,a,t,s){e={s:["m\xf5ne sekundi","m\xf5ni sekund","paar sekundit"],ss:[e+"sekundi",e+"sekundit"],m:["\xfche minuti","\xfcks minut"],mm:[e+" minuti",e+" minutit"],h:["\xfche tunni","tund aega","\xfcks tund"],hh:[e+" tunni",e+" tundi"],d:["\xfche p\xe4eva","\xfcks p\xe4ev"],M:["kuu aja","kuu aega","\xfcks kuu"],MM:[e+" kuu",e+" kuud"],y:["\xfche aasta","aasta","\xfcks aasta"],yy:[e+" aasta",e+" aastat"]};return a?e[t][2]||e[t][1]:s?e[t][0]:e[t][1]}M.defineLocale("es",{months:"enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?cs:hs)[e.month()]:hs},monthsRegex:n,monthsShortRegex:n,monthsStrictRegex:/^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i,monthsShortStrictRegex:/^(ene\.?|feb\.?|mar\.?|abr\.?|may\.?|jun\.?|jul\.?|ago\.?|sep\.?|oct\.?|nov\.?|dic\.?)/i,monthsParse:ge,longMonthsParse:ge,shortMonthsParse:ge,weekdays:"domingo_lunes_martes_mi\xe9rcoles_jueves_viernes_s\xe1bado".split("_"),weekdaysShort:"dom._lun._mar._mi\xe9._jue._vie._s\xe1b.".split("_"),weekdaysMin:"do_lu_ma_mi_ju_vi_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY H:mm",LLLL:"dddd, D [de] MMMM [de] YYYY H:mm"},calendar:{sameDay:function(){return"[hoy a la"+(1!==this.hours()?"s":"")+"] LT"},nextDay:function(){return"[ma\xf1ana a la"+(1!==this.hours()?"s":"")+"] LT"},nextWeek:function(){return"dddd [a la"+(1!==this.hours()?"s":"")+"] LT"},lastDay:function(){return"[ayer a la"+(1!==this.hours()?"s":"")+"] LT"},lastWeek:function(){return"[el] dddd [pasado a la"+(1!==this.hours()?"s":"")+"] LT"},sameElse:"L"},relativeTime:{future:"en %s",past:"hace %s",s:"unos segundos",ss:"%d segundos",m:"un minuto",mm:"%d minutos",h:"una hora",hh:"%d horas",d:"un d\xeda",dd:"%d d\xedas",w:"una semana",ww:"%d semanas",M:"un mes",MM:"%d meses",y:"un a\xf1o",yy:"%d a\xf1os"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4},invalidDate:"Fecha inv\xe1lida"}),M.defineLocale("et",{months:"jaanuar_veebruar_m\xe4rts_aprill_mai_juuni_juuli_august_september_oktoober_november_detsember".split("_"),monthsShort:"jaan_veebr_m\xe4rts_apr_mai_juuni_juuli_aug_sept_okt_nov_dets".split("_"),weekdays:"p\xfchap\xe4ev_esmasp\xe4ev_teisip\xe4ev_kolmap\xe4ev_neljap\xe4ev_reede_laup\xe4ev".split("_"),weekdaysShort:"P_E_T_K_N_R_L".split("_"),weekdaysMin:"P_E_T_K_N_R_L".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd, D. MMMM YYYY H:mm"},calendar:{sameDay:"[T\xe4na,] LT",nextDay:"[Homme,] LT",nextWeek:"[J\xe4rgmine] dddd LT",lastDay:"[Eile,] LT",lastWeek:"[Eelmine] dddd LT",sameElse:"L"},relativeTime:{future:"%s p\xe4rast",past:"%s tagasi",s:Ls,ss:Ls,m:Ls,mm:Ls,h:Ls,hh:Ls,d:Ls,dd:"%d p\xe4eva",M:Ls,MM:Ls,y:Ls,yy:Ls},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("eu",{months:"urtarrila_otsaila_martxoa_apirila_maiatza_ekaina_uztaila_abuztua_iraila_urria_azaroa_abendua".split("_"),monthsShort:"urt._ots._mar._api._mai._eka._uzt._abu._ira._urr._aza._abe.".split("_"),monthsParseExact:!0,weekdays:"igandea_astelehena_asteartea_asteazkena_osteguna_ostirala_larunbata".split("_"),weekdaysShort:"ig._al._ar._az._og._ol._lr.".split("_"),weekdaysMin:"ig_al_ar_az_og_ol_lr".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"YYYY[ko] MMMM[ren] D[a]",LLL:"YYYY[ko] MMMM[ren] D[a] HH:mm",LLLL:"dddd, YYYY[ko] MMMM[ren] D[a] HH:mm",l:"YYYY-M-D",ll:"YYYY[ko] MMM D[a]",lll:"YYYY[ko] MMM D[a] HH:mm",llll:"ddd, YYYY[ko] MMM D[a] HH:mm"},calendar:{sameDay:"[gaur] LT[etan]",nextDay:"[bihar] LT[etan]",nextWeek:"dddd LT[etan]",lastDay:"[atzo] LT[etan]",lastWeek:"[aurreko] dddd LT[etan]",sameElse:"L"},relativeTime:{future:"%s barru",past:"duela %s",s:"segundo batzuk",ss:"%d segundo",m:"minutu bat",mm:"%d minutu",h:"ordu bat",hh:"%d ordu",d:"egun bat",dd:"%d egun",M:"hilabete bat",MM:"%d hilabete",y:"urte bat",yy:"%d urte"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}});var Ys={1:"\u06f1",2:"\u06f2",3:"\u06f3",4:"\u06f4",5:"\u06f5",6:"\u06f6",7:"\u06f7",8:"\u06f8",9:"\u06f9",0:"\u06f0"},ys={"\u06f1":"1","\u06f2":"2","\u06f3":"3","\u06f4":"4","\u06f5":"5","\u06f6":"6","\u06f7":"7","\u06f8":"8","\u06f9":"9","\u06f0":"0"},fs=(M.defineLocale("fa",{months:"\u0698\u0627\u0646\u0648\u06cc\u0647_\u0641\u0648\u0631\u06cc\u0647_\u0645\u0627\u0631\u0633_\u0622\u0648\u0631\u06cc\u0644_\u0645\u0647_\u0698\u0648\u0626\u0646_\u0698\u0648\u0626\u06cc\u0647_\u0627\u0648\u062a_\u0633\u067e\u062a\u0627\u0645\u0628\u0631_\u0627\u06a9\u062a\u0628\u0631_\u0646\u0648\u0627\u0645\u0628\u0631_\u062f\u0633\u0627\u0645\u0628\u0631".split("_"),monthsShort:"\u0698\u0627\u0646\u0648\u06cc\u0647_\u0641\u0648\u0631\u06cc\u0647_\u0645\u0627\u0631\u0633_\u0622\u0648\u0631\u06cc\u0644_\u0645\u0647_\u0698\u0648\u0626\u0646_\u0698\u0648\u0626\u06cc\u0647_\u0627\u0648\u062a_\u0633\u067e\u062a\u0627\u0645\u0628\u0631_\u0627\u06a9\u062a\u0628\u0631_\u0646\u0648\u0627\u0645\u0628\u0631_\u062f\u0633\u0627\u0645\u0628\u0631".split("_"),weekdays:"\u06cc\u06a9\u200c\u0634\u0646\u0628\u0647_\u062f\u0648\u0634\u0646\u0628\u0647_\u0633\u0647\u200c\u0634\u0646\u0628\u0647_\u0686\u0647\u0627\u0631\u0634\u0646\u0628\u0647_\u067e\u0646\u062c\u200c\u0634\u0646\u0628\u0647_\u062c\u0645\u0639\u0647_\u0634\u0646\u0628\u0647".split("_"),weekdaysShort:"\u06cc\u06a9\u200c\u0634\u0646\u0628\u0647_\u062f\u0648\u0634\u0646\u0628\u0647_\u0633\u0647\u200c\u0634\u0646\u0628\u0647_\u0686\u0647\u0627\u0631\u0634\u0646\u0628\u0647_\u067e\u0646\u062c\u200c\u0634\u0646\u0628\u0647_\u062c\u0645\u0639\u0647_\u0634\u0646\u0628\u0647".split("_"),weekdaysMin:"\u06cc_\u062f_\u0633_\u0686_\u067e_\u062c_\u0634".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},meridiemParse:/\u0642\u0628\u0644 \u0627\u0632 \u0638\u0647\u0631|\u0628\u0639\u062f \u0627\u0632 \u0638\u0647\u0631/,isPM:function(e){return/\u0628\u0639\u062f \u0627\u0632 \u0638\u0647\u0631/.test(e)},meridiem:function(e,a,t){return e<12?"\u0642\u0628\u0644 \u0627\u0632 \u0638\u0647\u0631":"\u0628\u0639\u062f \u0627\u0632 \u0638\u0647\u0631"},calendar:{sameDay:"[\u0627\u0645\u0631\u0648\u0632 \u0633\u0627\u0639\u062a] LT",nextDay:"[\u0641\u0631\u062f\u0627 \u0633\u0627\u0639\u062a] LT",nextWeek:"dddd [\u0633\u0627\u0639\u062a] LT",lastDay:"[\u062f\u06cc\u0631\u0648\u0632 \u0633\u0627\u0639\u062a] LT",lastWeek:"dddd [\u067e\u06cc\u0634] [\u0633\u0627\u0639\u062a] LT",sameElse:"L"},relativeTime:{future:"\u062f\u0631 %s",past:"%s \u067e\u06cc\u0634",s:"\u0686\u0646\u062f \u062b\u0627\u0646\u06cc\u0647",ss:"%d \u062b\u0627\u0646\u06cc\u0647",m:"\u06cc\u06a9 \u062f\u0642\u06cc\u0642\u0647",mm:"%d \u062f\u0642\u06cc\u0642\u0647",h:"\u06cc\u06a9 \u0633\u0627\u0639\u062a",hh:"%d \u0633\u0627\u0639\u062a",d:"\u06cc\u06a9 \u0631\u0648\u0632",dd:"%d \u0631\u0648\u0632",M:"\u06cc\u06a9 \u0645\u0627\u0647",MM:"%d \u0645\u0627\u0647",y:"\u06cc\u06a9 \u0633\u0627\u0644",yy:"%d \u0633\u0627\u0644"},preparse:function(e){return e.replace(/[\u06f0-\u06f9]/g,function(e){return ys[e]}).replace(/\u060c/g,",")},postformat:function(e){return e.replace(/\d/g,function(e){return Ys[e]}).replace(/,/g,"\u060c")},dayOfMonthOrdinalParse:/\d{1,2}\u0645/,ordinal:"%d\u0645",week:{dow:6,doy:12}}),"nolla yksi kaksi kolme nelj\xe4 viisi kuusi seitsem\xe4n kahdeksan yhdeks\xe4n".split(" ")),ps=["nolla","yhden","kahden","kolmen","nelj\xe4n","viiden","kuuden",fs[7],fs[8],fs[9]];function O(e,a,t,s){var n="";switch(t){case"s":return s?"muutaman sekunnin":"muutama sekunti";case"ss":n=s?"sekunnin":"sekuntia";break;case"m":return s?"minuutin":"minuutti";case"mm":n=s?"minuutin":"minuuttia";break;case"h":return s?"tunnin":"tunti";case"hh":n=s?"tunnin":"tuntia";break;case"d":return s?"p\xe4iv\xe4n":"p\xe4iv\xe4";case"dd":n=s?"p\xe4iv\xe4n":"p\xe4iv\xe4\xe4";break;case"M":return s?"kuukauden":"kuukausi";case"MM":n=s?"kuukauden":"kuukautta";break;case"y":return s?"vuoden":"vuosi";case"yy":n=s?"vuoden":"vuotta";break}return t=s,n=((e=e)<10?(t?ps:fs)[e]:e)+" "+n}M.defineLocale("fi",{months:"tammikuu_helmikuu_maaliskuu_huhtikuu_toukokuu_kes\xe4kuu_hein\xe4kuu_elokuu_syyskuu_lokakuu_marraskuu_joulukuu".split("_"),monthsShort:"tammi_helmi_maalis_huhti_touko_kes\xe4_hein\xe4_elo_syys_loka_marras_joulu".split("_"),weekdays:"sunnuntai_maanantai_tiistai_keskiviikko_torstai_perjantai_lauantai".split("_"),weekdaysShort:"su_ma_ti_ke_to_pe_la".split("_"),weekdaysMin:"su_ma_ti_ke_to_pe_la".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD.MM.YYYY",LL:"Do MMMM[ta] YYYY",LLL:"Do MMMM[ta] YYYY, [klo] HH.mm",LLLL:"dddd, Do MMMM[ta] YYYY, [klo] HH.mm",l:"D.M.YYYY",ll:"Do MMM YYYY",lll:"Do MMM YYYY, [klo] HH.mm",llll:"ddd, Do MMM YYYY, [klo] HH.mm"},calendar:{sameDay:"[t\xe4n\xe4\xe4n] [klo] LT",nextDay:"[huomenna] [klo] LT",nextWeek:"dddd [klo] LT",lastDay:"[eilen] [klo] LT",lastWeek:"[viime] dddd[na] [klo] LT",sameElse:"L"},relativeTime:{future:"%s p\xe4\xe4st\xe4",past:"%s sitten",s:O,ss:O,m:O,mm:O,h:O,hh:O,d:O,dd:O,M:O,MM:O,y:O,yy:O},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("fil",{months:"Enero_Pebrero_Marso_Abril_Mayo_Hunyo_Hulyo_Agosto_Setyembre_Oktubre_Nobyembre_Disyembre".split("_"),monthsShort:"Ene_Peb_Mar_Abr_May_Hun_Hul_Ago_Set_Okt_Nob_Dis".split("_"),weekdays:"Linggo_Lunes_Martes_Miyerkules_Huwebes_Biyernes_Sabado".split("_"),weekdaysShort:"Lin_Lun_Mar_Miy_Huw_Biy_Sab".split("_"),weekdaysMin:"Li_Lu_Ma_Mi_Hu_Bi_Sab".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"MM/D/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY HH:mm",LLLL:"dddd, MMMM DD, YYYY HH:mm"},calendar:{sameDay:"LT [ngayong araw]",nextDay:"[Bukas ng] LT",nextWeek:"LT [sa susunod na] dddd",lastDay:"LT [kahapon]",lastWeek:"LT [noong nakaraang] dddd",sameElse:"L"},relativeTime:{future:"sa loob ng %s",past:"%s ang nakalipas",s:"ilang segundo",ss:"%d segundo",m:"isang minuto",mm:"%d minuto",h:"isang oras",hh:"%d oras",d:"isang araw",dd:"%d araw",M:"isang buwan",MM:"%d buwan",y:"isang taon",yy:"%d taon"},dayOfMonthOrdinalParse:/\d{1,2}/,ordinal:function(e){return e},week:{dow:1,doy:4}}),M.defineLocale("fo",{months:"januar_februar_mars_apr\xedl_mai_juni_juli_august_september_oktober_november_desember".split("_"),monthsShort:"jan_feb_mar_apr_mai_jun_jul_aug_sep_okt_nov_des".split("_"),weekdays:"sunnudagur_m\xe1nadagur_t\xfdsdagur_mikudagur_h\xf3sdagur_fr\xedggjadagur_leygardagur".split("_"),weekdaysShort:"sun_m\xe1n_t\xfds_mik_h\xf3s_fr\xed_ley".split("_"),weekdaysMin:"su_m\xe1_t\xfd_mi_h\xf3_fr_le".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D. MMMM, YYYY HH:mm"},calendar:{sameDay:"[\xcd dag kl.] LT",nextDay:"[\xcd morgin kl.] LT",nextWeek:"dddd [kl.] LT",lastDay:"[\xcd gj\xe1r kl.] LT",lastWeek:"[s\xed\xf0stu] dddd [kl] LT",sameElse:"L"},relativeTime:{future:"um %s",past:"%s s\xed\xf0ani",s:"f\xe1 sekund",ss:"%d sekundir",m:"ein minuttur",mm:"%d minuttir",h:"ein t\xedmi",hh:"%d t\xedmar",d:"ein dagur",dd:"%d dagar",M:"ein m\xe1na\xf0ur",MM:"%d m\xe1na\xf0ir",y:"eitt \xe1r",yy:"%d \xe1r"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("fr-ca",{months:"janvier_f\xe9vrier_mars_avril_mai_juin_juillet_ao\xfbt_septembre_octobre_novembre_d\xe9cembre".split("_"),monthsShort:"janv._f\xe9vr._mars_avr._mai_juin_juil._ao\xfbt_sept._oct._nov._d\xe9c.".split("_"),monthsParseExact:!0,weekdays:"dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"),weekdaysShort:"dim._lun._mar._mer._jeu._ven._sam.".split("_"),weekdaysMin:"di_lu_ma_me_je_ve_sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[Aujourd\u2019hui \xe0] LT",nextDay:"[Demain \xe0] LT",nextWeek:"dddd [\xe0] LT",lastDay:"[Hier \xe0] LT",lastWeek:"dddd [dernier \xe0] LT",sameElse:"L"},relativeTime:{future:"dans %s",past:"il y a %s",s:"quelques secondes",ss:"%d secondes",m:"une minute",mm:"%d minutes",h:"une heure",hh:"%d heures",d:"un jour",dd:"%d jours",M:"un mois",MM:"%d mois",y:"un an",yy:"%d ans"},dayOfMonthOrdinalParse:/\d{1,2}(er|e)/,ordinal:function(e,a){switch(a){default:case"M":case"Q":case"D":case"DDD":case"d":return e+(1===e?"er":"e");case"w":case"W":return e+(1===e?"re":"e")}}}),M.defineLocale("fr-ch",{months:"janvier_f\xe9vrier_mars_avril_mai_juin_juillet_ao\xfbt_septembre_octobre_novembre_d\xe9cembre".split("_"),monthsShort:"janv._f\xe9vr._mars_avr._mai_juin_juil._ao\xfbt_sept._oct._nov._d\xe9c.".split("_"),monthsParseExact:!0,weekdays:"dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"),weekdaysShort:"dim._lun._mar._mer._jeu._ven._sam.".split("_"),weekdaysMin:"di_lu_ma_me_je_ve_sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[Aujourd\u2019hui \xe0] LT",nextDay:"[Demain \xe0] LT",nextWeek:"dddd [\xe0] LT",lastDay:"[Hier \xe0] LT",lastWeek:"dddd [dernier \xe0] LT",sameElse:"L"},relativeTime:{future:"dans %s",past:"il y a %s",s:"quelques secondes",ss:"%d secondes",m:"une minute",mm:"%d minutes",h:"une heure",hh:"%d heures",d:"un jour",dd:"%d jours",M:"un mois",MM:"%d mois",y:"un an",yy:"%d ans"},dayOfMonthOrdinalParse:/\d{1,2}(er|e)/,ordinal:function(e,a){switch(a){default:case"M":case"Q":case"D":case"DDD":case"d":return e+(1===e?"er":"e");case"w":case"W":return e+(1===e?"re":"e")}},week:{dow:1,doy:4}});var De=/(janv\.?|f\xe9vr\.?|mars|avr\.?|mai|juin|juil\.?|ao\xfbt|sept\.?|oct\.?|nov\.?|d\xe9c\.?|janvier|f\xe9vrier|mars|avril|mai|juin|juillet|ao\xfbt|septembre|octobre|novembre|d\xe9cembre)/i,pe=[/^janv/i,/^f\xe9vr/i,/^mars/i,/^avr/i,/^mai/i,/^juin/i,/^juil/i,/^ao\xfbt/i,/^sept/i,/^oct/i,/^nov/i,/^d\xe9c/i],ks=(M.defineLocale("fr",{months:"janvier_f\xe9vrier_mars_avril_mai_juin_juillet_ao\xfbt_septembre_octobre_novembre_d\xe9cembre".split("_"),monthsShort:"janv._f\xe9vr._mars_avr._mai_juin_juil._ao\xfbt_sept._oct._nov._d\xe9c.".split("_"),monthsRegex:De,monthsShortRegex:De,monthsStrictRegex:/^(janvier|f\xe9vrier|mars|avril|mai|juin|juillet|ao\xfbt|septembre|octobre|novembre|d\xe9cembre)/i,monthsShortStrictRegex:/(janv\.?|f\xe9vr\.?|mars|avr\.?|mai|juin|juil\.?|ao\xfbt|sept\.?|oct\.?|nov\.?|d\xe9c\.?)/i,monthsParse:pe,longMonthsParse:pe,shortMonthsParse:pe,weekdays:"dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi".split("_"),weekdaysShort:"dim._lun._mar._mer._jeu._ven._sam.".split("_"),weekdaysMin:"di_lu_ma_me_je_ve_sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[Aujourd\u2019hui \xe0] LT",nextDay:"[Demain \xe0] LT",nextWeek:"dddd [\xe0] LT",lastDay:"[Hier \xe0] LT",lastWeek:"dddd [dernier \xe0] LT",sameElse:"L"},relativeTime:{future:"dans %s",past:"il y a %s",s:"quelques secondes",ss:"%d secondes",m:"une minute",mm:"%d minutes",h:"une heure",hh:"%d heures",d:"un jour",dd:"%d jours",w:"une semaine",ww:"%d semaines",M:"un mois",MM:"%d mois",y:"un an",yy:"%d ans"},dayOfMonthOrdinalParse:/\d{1,2}(er|)/,ordinal:function(e,a){switch(a){case"D":return e+(1===e?"er":"");default:case"M":case"Q":case"DDD":case"d":return e+(1===e?"er":"e");case"w":case"W":return e+(1===e?"re":"e")}},week:{dow:1,doy:4}}),"jan._feb._mrt._apr._mai_jun._jul._aug._sep._okt._nov._des.".split("_")),Ds="jan_feb_mrt_apr_mai_jun_jul_aug_sep_okt_nov_des".split("_");M.defineLocale("fy",{months:"jannewaris_febrewaris_maart_april_maaie_juny_july_augustus_septimber_oktober_novimber_desimber".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?Ds:ks)[e.month()]:ks},monthsParseExact:!0,weekdays:"snein_moandei_tiisdei_woansdei_tongersdei_freed_sneon".split("_"),weekdaysShort:"si._mo._ti._wo._to._fr._so.".split("_"),weekdaysMin:"Si_Mo_Ti_Wo_To_Fr_So".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD-MM-YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[hjoed om] LT",nextDay:"[moarn om] LT",nextWeek:"dddd [om] LT",lastDay:"[juster om] LT",lastWeek:"[\xf4fr\xfbne] dddd [om] LT",sameElse:"L"},relativeTime:{future:"oer %s",past:"%s lyn",s:"in pear sekonden",ss:"%d sekonden",m:"ien min\xfat",mm:"%d minuten",h:"ien oere",hh:"%d oeren",d:"ien dei",dd:"%d dagen",M:"ien moanne",MM:"%d moannen",y:"ien jier",yy:"%d jierren"},dayOfMonthOrdinalParse:/\d{1,2}(ste|de)/,ordinal:function(e){return e+(1===e||8===e||20<=e?"ste":"de")},week:{dow:1,doy:4}}),M.defineLocale("ga",{months:["Ean\xe1ir","Feabhra","M\xe1rta","Aibre\xe1n","Bealtaine","Meitheamh","I\xfail","L\xfanasa","Me\xe1n F\xf3mhair","Deireadh F\xf3mhair","Samhain","Nollaig"],monthsShort:["Ean","Feabh","M\xe1rt","Aib","Beal","Meith","I\xfail","L\xfan","M.F.","D.F.","Samh","Noll"],monthsParseExact:!0,weekdays:["D\xe9 Domhnaigh","D\xe9 Luain","D\xe9 M\xe1irt","D\xe9 C\xe9adaoin","D\xe9ardaoin","D\xe9 hAoine","D\xe9 Sathairn"],weekdaysShort:["Domh","Luan","M\xe1irt","C\xe9ad","D\xe9ar","Aoine","Sath"],weekdaysMin:["Do","Lu","M\xe1","C\xe9","D\xe9","A","Sa"],longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Inniu ag] LT",nextDay:"[Am\xe1rach ag] LT",nextWeek:"dddd [ag] LT",lastDay:"[Inn\xe9 ag] LT",lastWeek:"dddd [seo caite] [ag] LT",sameElse:"L"},relativeTime:{future:"i %s",past:"%s \xf3 shin",s:"c\xfapla soicind",ss:"%d soicind",m:"n\xf3im\xe9ad",mm:"%d n\xf3im\xe9ad",h:"uair an chloig",hh:"%d uair an chloig",d:"l\xe1",dd:"%d l\xe1",M:"m\xed",MM:"%d m\xedonna",y:"bliain",yy:"%d bliain"},dayOfMonthOrdinalParse:/\d{1,2}(d|na|mh)/,ordinal:function(e){return e+(1===e?"d":e%10==2?"na":"mh")},week:{dow:1,doy:4}});function Ts(e,a,t,s){e={s:["\u0925\u094b\u0921\u092f\u093e \u0938\u0945\u0915\u0902\u0921\u093e\u0902\u0928\u0940","\u0925\u094b\u0921\u0947 \u0938\u0945\u0915\u0902\u0921"],ss:[e+" \u0938\u0945\u0915\u0902\u0921\u093e\u0902\u0928\u0940",e+" \u0938\u0945\u0915\u0902\u0921"],m:["\u090f\u0915\u093e \u092e\u093f\u0923\u091f\u093e\u0928","\u090f\u0915 \u092e\u093f\u0928\u0942\u091f"],mm:[e+" \u092e\u093f\u0923\u091f\u093e\u0902\u0928\u0940",e+" \u092e\u093f\u0923\u091f\u093e\u0902"],h:["\u090f\u0915\u093e \u0935\u0930\u093e\u0928","\u090f\u0915 \u0935\u0930"],hh:[e+" \u0935\u0930\u093e\u0902\u0928\u0940",e+" \u0935\u0930\u093e\u0902"],d:["\u090f\u0915\u093e \u0926\u093f\u0938\u093e\u0928","\u090f\u0915 \u0926\u0940\u0938"],dd:[e+" \u0926\u093f\u0938\u093e\u0902\u0928\u0940",e+" \u0926\u0940\u0938"],M:["\u090f\u0915\u093e \u092e\u094d\u0939\u092f\u0928\u094d\u092f\u093e\u0928","\u090f\u0915 \u092e\u094d\u0939\u092f\u0928\u094b"],MM:[e+" \u092e\u094d\u0939\u092f\u0928\u094d\u092f\u093e\u0928\u0940",e+" \u092e\u094d\u0939\u092f\u0928\u0947"],y:["\u090f\u0915\u093e \u0935\u0930\u094d\u0938\u093e\u0928","\u090f\u0915 \u0935\u0930\u094d\u0938"],yy:[e+" \u0935\u0930\u094d\u0938\u093e\u0902\u0928\u0940",e+" \u0935\u0930\u094d\u0938\u093e\u0902"]};return s?e[t][0]:e[t][1]}function gs(e,a,t,s){e={s:["thoddea sekondamni","thodde sekond"],ss:[e+" sekondamni",e+" sekond"],m:["eka mintan","ek minut"],mm:[e+" mintamni",e+" mintam"],h:["eka voran","ek vor"],hh:[e+" voramni",e+" voram"],d:["eka disan","ek dis"],dd:[e+" disamni",e+" dis"],M:["eka mhoinean","ek mhoino"],MM:[e+" mhoineamni",e+" mhoine"],y:["eka vorsan","ek voros"],yy:[e+" vorsamni",e+" vorsam"]};return s?e[t][0]:e[t][1]}M.defineLocale("gd",{months:["Am Faoilleach","An Gearran","Am M\xe0rt","An Giblean","An C\xe8itean","An t-\xd2gmhios","An t-Iuchar","An L\xf9nastal","An t-Sultain","An D\xe0mhair","An t-Samhain","An D\xf9bhlachd"],monthsShort:["Faoi","Gear","M\xe0rt","Gibl","C\xe8it","\xd2gmh","Iuch","L\xf9n","Sult","D\xe0mh","Samh","D\xf9bh"],monthsParseExact:!0,weekdays:["Did\xf2mhnaich","Diluain","Dim\xe0irt","Diciadain","Diardaoin","Dihaoine","Disathairne"],weekdaysShort:["Did","Dil","Dim","Dic","Dia","Dih","Dis"],weekdaysMin:["D\xf2","Lu","M\xe0","Ci","Ar","Ha","Sa"],longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[An-diugh aig] LT",nextDay:"[A-m\xe0ireach aig] LT",nextWeek:"dddd [aig] LT",lastDay:"[An-d\xe8 aig] LT",lastWeek:"dddd [seo chaidh] [aig] LT",sameElse:"L"},relativeTime:{future:"ann an %s",past:"bho chionn %s",s:"beagan diogan",ss:"%d diogan",m:"mionaid",mm:"%d mionaidean",h:"uair",hh:"%d uairean",d:"latha",dd:"%d latha",M:"m\xecos",MM:"%d m\xecosan",y:"bliadhna",yy:"%d bliadhna"},dayOfMonthOrdinalParse:/\d{1,2}(d|na|mh)/,ordinal:function(e){return e+(1===e?"d":e%10==2?"na":"mh")},week:{dow:1,doy:4}}),M.defineLocale("gl",{months:"xaneiro_febreiro_marzo_abril_maio_xu\xf1o_xullo_agosto_setembro_outubro_novembro_decembro".split("_"),monthsShort:"xan._feb._mar._abr._mai._xu\xf1._xul._ago._set._out._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"domingo_luns_martes_m\xe9rcores_xoves_venres_s\xe1bado".split("_"),weekdaysShort:"dom._lun._mar._m\xe9r._xov._ven._s\xe1b.".split("_"),weekdaysMin:"do_lu_ma_m\xe9_xo_ve_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY H:mm",LLLL:"dddd, D [de] MMMM [de] YYYY H:mm"},calendar:{sameDay:function(){return"[hoxe "+(1!==this.hours()?"\xe1s":"\xe1")+"] LT"},nextDay:function(){return"[ma\xf1\xe1 "+(1!==this.hours()?"\xe1s":"\xe1")+"] LT"},nextWeek:function(){return"dddd ["+(1!==this.hours()?"\xe1s":"a")+"] LT"},lastDay:function(){return"[onte "+(1!==this.hours()?"\xe1":"a")+"] LT"},lastWeek:function(){return"[o] dddd [pasado "+(1!==this.hours()?"\xe1s":"a")+"] LT"},sameElse:"L"},relativeTime:{future:function(e){return 0===e.indexOf("un")?"n"+e:"en "+e},past:"hai %s",s:"uns segundos",ss:"%d segundos",m:"un minuto",mm:"%d minutos",h:"unha hora",hh:"%d horas",d:"un d\xeda",dd:"%d d\xedas",M:"un mes",MM:"%d meses",y:"un ano",yy:"%d anos"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),M.defineLocale("gom-deva",{months:{standalone:"\u091c\u093e\u0928\u0947\u0935\u093e\u0930\u0940_\u092b\u0947\u092c\u094d\u0930\u0941\u0935\u093e\u0930\u0940_\u092e\u093e\u0930\u094d\u091a_\u090f\u092a\u094d\u0930\u0940\u0932_\u092e\u0947_\u091c\u0942\u0928_\u091c\u0941\u0932\u092f_\u0911\u0917\u0938\u094d\u091f_\u0938\u092a\u094d\u091f\u0947\u0902\u092c\u0930_\u0911\u0915\u094d\u091f\u094b\u092c\u0930_\u0928\u094b\u0935\u094d\u0939\u0947\u0902\u092c\u0930_\u0921\u093f\u0938\u0947\u0902\u092c\u0930".split("_"),format:"\u091c\u093e\u0928\u0947\u0935\u093e\u0930\u0940\u091a\u094d\u092f\u093e_\u092b\u0947\u092c\u094d\u0930\u0941\u0935\u093e\u0930\u0940\u091a\u094d\u092f\u093e_\u092e\u093e\u0930\u094d\u091a\u093e\u091a\u094d\u092f\u093e_\u090f\u092a\u094d\u0930\u0940\u0932\u093e\u091a\u094d\u092f\u093e_\u092e\u0947\u092f\u093e\u091a\u094d\u092f\u093e_\u091c\u0942\u0928\u093e\u091a\u094d\u092f\u093e_\u091c\u0941\u0932\u092f\u093e\u091a\u094d\u092f\u093e_\u0911\u0917\u0938\u094d\u091f\u093e\u091a\u094d\u092f\u093e_\u0938\u092a\u094d\u091f\u0947\u0902\u092c\u0930\u093e\u091a\u094d\u092f\u093e_\u0911\u0915\u094d\u091f\u094b\u092c\u0930\u093e\u091a\u094d\u092f\u093e_\u0928\u094b\u0935\u094d\u0939\u0947\u0902\u092c\u0930\u093e\u091a\u094d\u092f\u093e_\u0921\u093f\u0938\u0947\u0902\u092c\u0930\u093e\u091a\u094d\u092f\u093e".split("_"),isFormat:/MMMM(\s)+D[oD]?/},monthsShort:"\u091c\u093e\u0928\u0947._\u092b\u0947\u092c\u094d\u0930\u0941._\u092e\u093e\u0930\u094d\u091a_\u090f\u092a\u094d\u0930\u0940._\u092e\u0947_\u091c\u0942\u0928_\u091c\u0941\u0932._\u0911\u0917._\u0938\u092a\u094d\u091f\u0947\u0902._\u0911\u0915\u094d\u091f\u094b._\u0928\u094b\u0935\u094d\u0939\u0947\u0902._\u0921\u093f\u0938\u0947\u0902.".split("_"),monthsParseExact:!0,weekdays:"\u0906\u092f\u0924\u093e\u0930_\u0938\u094b\u092e\u093e\u0930_\u092e\u0902\u0917\u0933\u093e\u0930_\u092c\u0941\u0927\u0935\u093e\u0930_\u092c\u093f\u0930\u0947\u0938\u094d\u0924\u093e\u0930_\u0938\u0941\u0915\u094d\u0930\u093e\u0930_\u0936\u0947\u0928\u0935\u093e\u0930".split("_"),weekdaysShort:"\u0906\u092f\u0924._\u0938\u094b\u092e._\u092e\u0902\u0917\u0933._\u092c\u0941\u0927._\u092c\u094d\u0930\u0947\u0938\u094d\u0924._\u0938\u0941\u0915\u094d\u0930._\u0936\u0947\u0928.".split("_"),weekdaysMin:"\u0906_\u0938\u094b_\u092e\u0902_\u092c\u0941_\u092c\u094d\u0930\u0947_\u0938\u0941_\u0936\u0947".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"A h:mm [\u0935\u093e\u091c\u0924\u093e\u0902]",LTS:"A h:mm:ss [\u0935\u093e\u091c\u0924\u093e\u0902]",L:"DD-MM-YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY A h:mm [\u0935\u093e\u091c\u0924\u093e\u0902]",LLLL:"dddd, MMMM Do, YYYY, A h:mm [\u0935\u093e\u091c\u0924\u093e\u0902]",llll:"ddd, D MMM YYYY, A h:mm [\u0935\u093e\u091c\u0924\u093e\u0902]"},calendar:{sameDay:"[\u0906\u092f\u091c] LT",nextDay:"[\u092b\u093e\u0932\u094d\u092f\u093e\u0902] LT",nextWeek:"[\u092b\u0941\u0921\u0932\u094b] dddd[,] LT",lastDay:"[\u0915\u093e\u0932] LT",lastWeek:"[\u092b\u093e\u091f\u0932\u094b] dddd[,] LT",sameElse:"L"},relativeTime:{future:"%s",past:"%s \u0906\u0926\u0940\u0902",s:Ts,ss:Ts,m:Ts,mm:Ts,h:Ts,hh:Ts,d:Ts,dd:Ts,M:Ts,MM:Ts,y:Ts,yy:Ts},dayOfMonthOrdinalParse:/\d{1,2}(\u0935\u0947\u0930)/,ordinal:function(e,a){switch(a){case"D":return e+"\u0935\u0947\u0930";default:case"M":case"Q":case"DDD":case"d":case"w":case"W":return e}},week:{dow:0,doy:3},meridiemParse:/\u0930\u093e\u0924\u0940|\u0938\u0915\u093e\u0933\u0940\u0902|\u0926\u0928\u092a\u093e\u0930\u093e\u0902|\u0938\u093e\u0902\u091c\u0947/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0930\u093e\u0924\u0940"===a?e<4?e:e+12:"\u0938\u0915\u093e\u0933\u0940\u0902"===a?e:"\u0926\u0928\u092a\u093e\u0930\u093e\u0902"===a?12<e?e:e+12:"\u0938\u093e\u0902\u091c\u0947"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0930\u093e\u0924\u0940":e<12?"\u0938\u0915\u093e\u0933\u0940\u0902":e<16?"\u0926\u0928\u092a\u093e\u0930\u093e\u0902":e<20?"\u0938\u093e\u0902\u091c\u0947":"\u0930\u093e\u0924\u0940"}}),M.defineLocale("gom-latn",{months:{standalone:"Janer_Febrer_Mars_Abril_Mai_Jun_Julai_Agost_Setembr_Otubr_Novembr_Dezembr".split("_"),format:"Janerachea_Febrerachea_Marsachea_Abrilachea_Maiachea_Junachea_Julaiachea_Agostachea_Setembrachea_Otubrachea_Novembrachea_Dezembrachea".split("_"),isFormat:/MMMM(\s)+D[oD]?/},monthsShort:"Jan._Feb._Mars_Abr._Mai_Jun_Jul._Ago._Set._Otu._Nov._Dez.".split("_"),monthsParseExact:!0,weekdays:"Aitar_Somar_Mongllar_Budhvar_Birestar_Sukrar_Son'var".split("_"),weekdaysShort:"Ait._Som._Mon._Bud._Bre._Suk._Son.".split("_"),weekdaysMin:"Ai_Sm_Mo_Bu_Br_Su_Sn".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"A h:mm [vazta]",LTS:"A h:mm:ss [vazta]",L:"DD-MM-YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY A h:mm [vazta]",LLLL:"dddd, MMMM Do, YYYY, A h:mm [vazta]",llll:"ddd, D MMM YYYY, A h:mm [vazta]"},calendar:{sameDay:"[Aiz] LT",nextDay:"[Faleam] LT",nextWeek:"[Fuddlo] dddd[,] LT",lastDay:"[Kal] LT",lastWeek:"[Fattlo] dddd[,] LT",sameElse:"L"},relativeTime:{future:"%s",past:"%s adim",s:gs,ss:gs,m:gs,mm:gs,h:gs,hh:gs,d:gs,dd:gs,M:gs,MM:gs,y:gs,yy:gs},dayOfMonthOrdinalParse:/\d{1,2}(er)/,ordinal:function(e,a){switch(a){case"D":return e+"er";default:case"M":case"Q":case"DDD":case"d":case"w":case"W":return e}},week:{dow:0,doy:3},meridiemParse:/rati|sokallim|donparam|sanje/,meridiemHour:function(e,a){return 12===e&&(e=0),"rati"===a?e<4?e:e+12:"sokallim"===a?e:"donparam"===a?12<e?e:e+12:"sanje"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"rati":e<12?"sokallim":e<16?"donparam":e<20?"sanje":"rati"}});var ws={1:"\u0ae7",2:"\u0ae8",3:"\u0ae9",4:"\u0aea",5:"\u0aeb",6:"\u0aec",7:"\u0aed",8:"\u0aee",9:"\u0aef",0:"\u0ae6"},bs={"\u0ae7":"1","\u0ae8":"2","\u0ae9":"3","\u0aea":"4","\u0aeb":"5","\u0aec":"6","\u0aed":"7","\u0aee":"8","\u0aef":"9","\u0ae6":"0"},Ss=(M.defineLocale("gu",{months:"\u0a9c\u0abe\u0aa8\u0acd\u0aaf\u0ac1\u0a86\u0ab0\u0ac0_\u0aab\u0ac7\u0aac\u0acd\u0ab0\u0ac1\u0a86\u0ab0\u0ac0_\u0aae\u0abe\u0ab0\u0acd\u0a9a_\u0a8f\u0aaa\u0acd\u0ab0\u0abf\u0ab2_\u0aae\u0ac7_\u0a9c\u0ac2\u0aa8_\u0a9c\u0ac1\u0ab2\u0abe\u0a88_\u0a91\u0a97\u0ab8\u0acd\u0a9f_\u0ab8\u0aaa\u0acd\u0a9f\u0ac7\u0aae\u0acd\u0aac\u0ab0_\u0a91\u0a95\u0acd\u0a9f\u0acd\u0aac\u0ab0_\u0aa8\u0ab5\u0ac7\u0aae\u0acd\u0aac\u0ab0_\u0aa1\u0abf\u0ab8\u0ac7\u0aae\u0acd\u0aac\u0ab0".split("_"),monthsShort:"\u0a9c\u0abe\u0aa8\u0acd\u0aaf\u0ac1._\u0aab\u0ac7\u0aac\u0acd\u0ab0\u0ac1._\u0aae\u0abe\u0ab0\u0acd\u0a9a_\u0a8f\u0aaa\u0acd\u0ab0\u0abf._\u0aae\u0ac7_\u0a9c\u0ac2\u0aa8_\u0a9c\u0ac1\u0ab2\u0abe._\u0a91\u0a97._\u0ab8\u0aaa\u0acd\u0a9f\u0ac7._\u0a91\u0a95\u0acd\u0a9f\u0acd._\u0aa8\u0ab5\u0ac7._\u0aa1\u0abf\u0ab8\u0ac7.".split("_"),monthsParseExact:!0,weekdays:"\u0ab0\u0ab5\u0abf\u0ab5\u0abe\u0ab0_\u0ab8\u0acb\u0aae\u0ab5\u0abe\u0ab0_\u0aae\u0a82\u0a97\u0ab3\u0ab5\u0abe\u0ab0_\u0aac\u0ac1\u0aa7\u0acd\u0ab5\u0abe\u0ab0_\u0a97\u0ac1\u0ab0\u0ac1\u0ab5\u0abe\u0ab0_\u0ab6\u0ac1\u0a95\u0acd\u0ab0\u0ab5\u0abe\u0ab0_\u0ab6\u0aa8\u0abf\u0ab5\u0abe\u0ab0".split("_"),weekdaysShort:"\u0ab0\u0ab5\u0abf_\u0ab8\u0acb\u0aae_\u0aae\u0a82\u0a97\u0ab3_\u0aac\u0ac1\u0aa7\u0acd_\u0a97\u0ac1\u0ab0\u0ac1_\u0ab6\u0ac1\u0a95\u0acd\u0ab0_\u0ab6\u0aa8\u0abf".split("_"),weekdaysMin:"\u0ab0_\u0ab8\u0acb_\u0aae\u0a82_\u0aac\u0ac1_\u0a97\u0ac1_\u0ab6\u0ac1_\u0ab6".split("_"),longDateFormat:{LT:"A h:mm \u0ab5\u0abe\u0a97\u0acd\u0aaf\u0ac7",LTS:"A h:mm:ss \u0ab5\u0abe\u0a97\u0acd\u0aaf\u0ac7",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u0ab5\u0abe\u0a97\u0acd\u0aaf\u0ac7",LLLL:"dddd, D MMMM YYYY, A h:mm \u0ab5\u0abe\u0a97\u0acd\u0aaf\u0ac7"},calendar:{sameDay:"[\u0a86\u0a9c] LT",nextDay:"[\u0a95\u0abe\u0ab2\u0ac7] LT",nextWeek:"dddd, LT",lastDay:"[\u0a97\u0a87\u0a95\u0abe\u0ab2\u0ac7] LT",lastWeek:"[\u0aaa\u0abe\u0a9b\u0ab2\u0abe] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0aae\u0abe",past:"%s \u0aaa\u0ab9\u0ac7\u0ab2\u0abe",s:"\u0a85\u0aae\u0ac1\u0a95 \u0aaa\u0ab3\u0acb",ss:"%d \u0ab8\u0ac7\u0a95\u0a82\u0aa1",m:"\u0a8f\u0a95 \u0aae\u0abf\u0aa8\u0abf\u0a9f",mm:"%d \u0aae\u0abf\u0aa8\u0abf\u0a9f",h:"\u0a8f\u0a95 \u0a95\u0ab2\u0abe\u0a95",hh:"%d \u0a95\u0ab2\u0abe\u0a95",d:"\u0a8f\u0a95 \u0aa6\u0abf\u0ab5\u0ab8",dd:"%d \u0aa6\u0abf\u0ab5\u0ab8",M:"\u0a8f\u0a95 \u0aae\u0ab9\u0abf\u0aa8\u0acb",MM:"%d \u0aae\u0ab9\u0abf\u0aa8\u0acb",y:"\u0a8f\u0a95 \u0ab5\u0ab0\u0acd\u0ab7",yy:"%d \u0ab5\u0ab0\u0acd\u0ab7"},preparse:function(e){return e.replace(/[\u0ae7\u0ae8\u0ae9\u0aea\u0aeb\u0aec\u0aed\u0aee\u0aef\u0ae6]/g,function(e){return bs[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return ws[e]})},meridiemParse:/\u0ab0\u0abe\u0aa4|\u0aac\u0aaa\u0acb\u0ab0|\u0ab8\u0ab5\u0abe\u0ab0|\u0ab8\u0abe\u0a82\u0a9c/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0ab0\u0abe\u0aa4"===a?e<4?e:e+12:"\u0ab8\u0ab5\u0abe\u0ab0"===a?e:"\u0aac\u0aaa\u0acb\u0ab0"===a?10<=e?e:e+12:"\u0ab8\u0abe\u0a82\u0a9c"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0ab0\u0abe\u0aa4":e<10?"\u0ab8\u0ab5\u0abe\u0ab0":e<17?"\u0aac\u0aaa\u0acb\u0ab0":e<20?"\u0ab8\u0abe\u0a82\u0a9c":"\u0ab0\u0abe\u0aa4"},week:{dow:0,doy:6}}),M.defineLocale("he",{months:"\u05d9\u05e0\u05d5\u05d0\u05e8_\u05e4\u05d1\u05e8\u05d5\u05d0\u05e8_\u05de\u05e8\u05e5_\u05d0\u05e4\u05e8\u05d9\u05dc_\u05de\u05d0\u05d9_\u05d9\u05d5\u05e0\u05d9_\u05d9\u05d5\u05dc\u05d9_\u05d0\u05d5\u05d2\u05d5\u05e1\u05d8_\u05e1\u05e4\u05d8\u05de\u05d1\u05e8_\u05d0\u05d5\u05e7\u05d8\u05d5\u05d1\u05e8_\u05e0\u05d5\u05d1\u05de\u05d1\u05e8_\u05d3\u05e6\u05de\u05d1\u05e8".split("_"),monthsShort:"\u05d9\u05e0\u05d5\u05f3_\u05e4\u05d1\u05e8\u05f3_\u05de\u05e8\u05e5_\u05d0\u05e4\u05e8\u05f3_\u05de\u05d0\u05d9_\u05d9\u05d5\u05e0\u05d9_\u05d9\u05d5\u05dc\u05d9_\u05d0\u05d5\u05d2\u05f3_\u05e1\u05e4\u05d8\u05f3_\u05d0\u05d5\u05e7\u05f3_\u05e0\u05d5\u05d1\u05f3_\u05d3\u05e6\u05de\u05f3".split("_"),weekdays:"\u05e8\u05d0\u05e9\u05d5\u05df_\u05e9\u05e0\u05d9_\u05e9\u05dc\u05d9\u05e9\u05d9_\u05e8\u05d1\u05d9\u05e2\u05d9_\u05d7\u05de\u05d9\u05e9\u05d9_\u05e9\u05d9\u05e9\u05d9_\u05e9\u05d1\u05ea".split("_"),weekdaysShort:"\u05d0\u05f3_\u05d1\u05f3_\u05d2\u05f3_\u05d3\u05f3_\u05d4\u05f3_\u05d5\u05f3_\u05e9\u05f3".split("_"),weekdaysMin:"\u05d0_\u05d1_\u05d2_\u05d3_\u05d4_\u05d5_\u05e9".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D [\u05d1]MMMM YYYY",LLL:"D [\u05d1]MMMM YYYY HH:mm",LLLL:"dddd, D [\u05d1]MMMM YYYY HH:mm",l:"D/M/YYYY",ll:"D MMM YYYY",lll:"D MMM YYYY HH:mm",llll:"ddd, D MMM YYYY HH:mm"},calendar:{sameDay:"[\u05d4\u05d9\u05d5\u05dd \u05d1\u05be]LT",nextDay:"[\u05de\u05d7\u05e8 \u05d1\u05be]LT",nextWeek:"dddd [\u05d1\u05e9\u05e2\u05d4] LT",lastDay:"[\u05d0\u05ea\u05de\u05d5\u05dc \u05d1\u05be]LT",lastWeek:"[\u05d1\u05d9\u05d5\u05dd] dddd [\u05d4\u05d0\u05d7\u05e8\u05d5\u05df \u05d1\u05e9\u05e2\u05d4] LT",sameElse:"L"},relativeTime:{future:"\u05d1\u05e2\u05d5\u05d3 %s",past:"\u05dc\u05e4\u05e0\u05d9 %s",s:"\u05de\u05e1\u05e4\u05e8 \u05e9\u05e0\u05d9\u05d5\u05ea",ss:"%d \u05e9\u05e0\u05d9\u05d5\u05ea",m:"\u05d3\u05e7\u05d4",mm:"%d \u05d3\u05e7\u05d5\u05ea",h:"\u05e9\u05e2\u05d4",hh:function(e){return 2===e?"\u05e9\u05e2\u05ea\u05d9\u05d9\u05dd":e+" \u05e9\u05e2\u05d5\u05ea"},d:"\u05d9\u05d5\u05dd",dd:function(e){return 2===e?"\u05d9\u05d5\u05de\u05d9\u05d9\u05dd":e+" \u05d9\u05de\u05d9\u05dd"},M:"\u05d7\u05d5\u05d3\u05e9",MM:function(e){return 2===e?"\u05d7\u05d5\u05d3\u05e9\u05d9\u05d9\u05dd":e+" \u05d7\u05d5\u05d3\u05e9\u05d9\u05dd"},y:"\u05e9\u05e0\u05d4",yy:function(e){return 2===e?"\u05e9\u05e0\u05ea\u05d9\u05d9\u05dd":e%10==0&&10!==e?e+" \u05e9\u05e0\u05d4":e+" \u05e9\u05e0\u05d9\u05dd"}},meridiemParse:/\u05d0\u05d7\u05d4"\u05e6|\u05dc\u05e4\u05e0\u05d4"\u05e6|\u05d0\u05d7\u05e8\u05d9 \u05d4\u05e6\u05d4\u05e8\u05d9\u05d9\u05dd|\u05dc\u05e4\u05e0\u05d9 \u05d4\u05e6\u05d4\u05e8\u05d9\u05d9\u05dd|\u05dc\u05e4\u05e0\u05d5\u05ea \u05d1\u05d5\u05e7\u05e8|\u05d1\u05d1\u05d5\u05e7\u05e8|\u05d1\u05e2\u05e8\u05d1/i,isPM:function(e){return/^(\u05d0\u05d7\u05d4"\u05e6|\u05d0\u05d7\u05e8\u05d9 \u05d4\u05e6\u05d4\u05e8\u05d9\u05d9\u05dd|\u05d1\u05e2\u05e8\u05d1)$/.test(e)},meridiem:function(e,a,t){return e<5?"\u05dc\u05e4\u05e0\u05d5\u05ea \u05d1\u05d5\u05e7\u05e8":e<10?"\u05d1\u05d1\u05d5\u05e7\u05e8":e<12?t?'\u05dc\u05e4\u05e0\u05d4"\u05e6':"\u05dc\u05e4\u05e0\u05d9 \u05d4\u05e6\u05d4\u05e8\u05d9\u05d9\u05dd":e<18?t?'\u05d0\u05d7\u05d4"\u05e6':"\u05d0\u05d7\u05e8\u05d9 \u05d4\u05e6\u05d4\u05e8\u05d9\u05d9\u05dd":"\u05d1\u05e2\u05e8\u05d1"}}),{1:"\u0967",2:"\u0968",3:"\u0969",4:"\u096a",5:"\u096b",6:"\u096c",7:"\u096d",8:"\u096e",9:"\u096f",0:"\u0966"}),Hs={"\u0967":"1","\u0968":"2","\u0969":"3","\u096a":"4","\u096b":"5","\u096c":"6","\u096d":"7","\u096e":"8","\u096f":"9","\u0966":"0"},Ne=[/^\u091c\u0928/i,/^\u092b\u093c\u0930|\u092b\u0930/i,/^\u092e\u093e\u0930\u094d\u091a/i,/^\u0905\u092a\u094d\u0930\u0948/i,/^\u092e\u0908/i,/^\u091c\u0942\u0928/i,/^\u091c\u0941\u0932/i,/^\u0905\u0917/i,/^\u0938\u093f\u0924\u0902|\u0938\u093f\u0924/i,/^\u0905\u0915\u094d\u091f\u0942/i,/^\u0928\u0935|\u0928\u0935\u0902/i,/^\u0926\u093f\u0938\u0902|\u0926\u093f\u0938/i];function vs(e,a,t){var s=e+" ";switch(t){case"ss":return s+=1===e?"sekunda":2===e||3===e||4===e?"sekunde":"sekundi";case"m":return a?"jedna minuta":"jedne minute";case"mm":return s+=1!==e&&(2===e||3===e||4===e)?"minute":"minuta";case"h":return a?"jedan sat":"jednog sata";case"hh":return s+=1===e?"sat":2===e||3===e||4===e?"sata":"sati";case"dd":return s+=1===e?"dan":"dana";case"MM":return s+=1===e?"mjesec":2===e||3===e||4===e?"mjeseca":"mjeseci";case"yy":return s+=1!==e&&(2===e||3===e||4===e)?"godine":"godina"}}M.defineLocale("hi",{months:{format:"\u091c\u0928\u0935\u0930\u0940_\u092b\u093c\u0930\u0935\u0930\u0940_\u092e\u093e\u0930\u094d\u091a_\u0905\u092a\u094d\u0930\u0948\u0932_\u092e\u0908_\u091c\u0942\u0928_\u091c\u0941\u0932\u093e\u0908_\u0905\u0917\u0938\u094d\u0924_\u0938\u093f\u0924\u092e\u094d\u092c\u0930_\u0905\u0915\u094d\u091f\u0942\u092c\u0930_\u0928\u0935\u092e\u094d\u092c\u0930_\u0926\u093f\u0938\u092e\u094d\u092c\u0930".split("_"),standalone:"\u091c\u0928\u0935\u0930\u0940_\u092b\u0930\u0935\u0930\u0940_\u092e\u093e\u0930\u094d\u091a_\u0905\u092a\u094d\u0930\u0948\u0932_\u092e\u0908_\u091c\u0942\u0928_\u091c\u0941\u0932\u093e\u0908_\u0905\u0917\u0938\u094d\u0924_\u0938\u093f\u0924\u0902\u092c\u0930_\u0905\u0915\u094d\u091f\u0942\u092c\u0930_\u0928\u0935\u0902\u092c\u0930_\u0926\u093f\u0938\u0902\u092c\u0930".split("_")},monthsShort:"\u091c\u0928._\u092b\u093c\u0930._\u092e\u093e\u0930\u094d\u091a_\u0905\u092a\u094d\u0930\u0948._\u092e\u0908_\u091c\u0942\u0928_\u091c\u0941\u0932._\u0905\u0917._\u0938\u093f\u0924._\u0905\u0915\u094d\u091f\u0942._\u0928\u0935._\u0926\u093f\u0938.".split("_"),weekdays:"\u0930\u0935\u093f\u0935\u093e\u0930_\u0938\u094b\u092e\u0935\u093e\u0930_\u092e\u0902\u0917\u0932\u0935\u093e\u0930_\u092c\u0941\u0927\u0935\u093e\u0930_\u0917\u0941\u0930\u0942\u0935\u093e\u0930_\u0936\u0941\u0915\u094d\u0930\u0935\u093e\u0930_\u0936\u0928\u093f\u0935\u093e\u0930".split("_"),weekdaysShort:"\u0930\u0935\u093f_\u0938\u094b\u092e_\u092e\u0902\u0917\u0932_\u092c\u0941\u0927_\u0917\u0941\u0930\u0942_\u0936\u0941\u0915\u094d\u0930_\u0936\u0928\u093f".split("_"),weekdaysMin:"\u0930_\u0938\u094b_\u092e\u0902_\u092c\u0941_\u0917\u0941_\u0936\u0941_\u0936".split("_"),longDateFormat:{LT:"A h:mm \u092c\u091c\u0947",LTS:"A h:mm:ss \u092c\u091c\u0947",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u092c\u091c\u0947",LLLL:"dddd, D MMMM YYYY, A h:mm \u092c\u091c\u0947"},monthsParse:Ne,longMonthsParse:Ne,shortMonthsParse:[/^\u091c\u0928/i,/^\u092b\u093c\u0930/i,/^\u092e\u093e\u0930\u094d\u091a/i,/^\u0905\u092a\u094d\u0930\u0948/i,/^\u092e\u0908/i,/^\u091c\u0942\u0928/i,/^\u091c\u0941\u0932/i,/^\u0905\u0917/i,/^\u0938\u093f\u0924/i,/^\u0905\u0915\u094d\u091f\u0942/i,/^\u0928\u0935/i,/^\u0926\u093f\u0938/i],monthsRegex:/^(\u091c\u0928\u0935\u0930\u0940|\u091c\u0928\.?|\u092b\u093c\u0930\u0935\u0930\u0940|\u092b\u0930\u0935\u0930\u0940|\u092b\u093c\u0930\.?|\u092e\u093e\u0930\u094d\u091a?|\u0905\u092a\u094d\u0930\u0948\u0932|\u0905\u092a\u094d\u0930\u0948\.?|\u092e\u0908?|\u091c\u0942\u0928?|\u091c\u0941\u0932\u093e\u0908|\u091c\u0941\u0932\.?|\u0905\u0917\u0938\u094d\u0924|\u0905\u0917\.?|\u0938\u093f\u0924\u092e\u094d\u092c\u0930|\u0938\u093f\u0924\u0902\u092c\u0930|\u0938\u093f\u0924\.?|\u0905\u0915\u094d\u091f\u0942\u092c\u0930|\u0905\u0915\u094d\u091f\u0942\.?|\u0928\u0935\u092e\u094d\u092c\u0930|\u0928\u0935\u0902\u092c\u0930|\u0928\u0935\.?|\u0926\u093f\u0938\u092e\u094d\u092c\u0930|\u0926\u093f\u0938\u0902\u092c\u0930|\u0926\u093f\u0938\.?)/i,monthsShortRegex:/^(\u091c\u0928\u0935\u0930\u0940|\u091c\u0928\.?|\u092b\u093c\u0930\u0935\u0930\u0940|\u092b\u0930\u0935\u0930\u0940|\u092b\u093c\u0930\.?|\u092e\u093e\u0930\u094d\u091a?|\u0905\u092a\u094d\u0930\u0948\u0932|\u0905\u092a\u094d\u0930\u0948\.?|\u092e\u0908?|\u091c\u0942\u0928?|\u091c\u0941\u0932\u093e\u0908|\u091c\u0941\u0932\.?|\u0905\u0917\u0938\u094d\u0924|\u0905\u0917\.?|\u0938\u093f\u0924\u092e\u094d\u092c\u0930|\u0938\u093f\u0924\u0902\u092c\u0930|\u0938\u093f\u0924\.?|\u0905\u0915\u094d\u091f\u0942\u092c\u0930|\u0905\u0915\u094d\u091f\u0942\.?|\u0928\u0935\u092e\u094d\u092c\u0930|\u0928\u0935\u0902\u092c\u0930|\u0928\u0935\.?|\u0926\u093f\u0938\u092e\u094d\u092c\u0930|\u0926\u093f\u0938\u0902\u092c\u0930|\u0926\u093f\u0938\.?)/i,monthsStrictRegex:/^(\u091c\u0928\u0935\u0930\u0940?|\u092b\u093c\u0930\u0935\u0930\u0940|\u092b\u0930\u0935\u0930\u0940?|\u092e\u093e\u0930\u094d\u091a?|\u0905\u092a\u094d\u0930\u0948\u0932?|\u092e\u0908?|\u091c\u0942\u0928?|\u091c\u0941\u0932\u093e\u0908?|\u0905\u0917\u0938\u094d\u0924?|\u0938\u093f\u0924\u092e\u094d\u092c\u0930|\u0938\u093f\u0924\u0902\u092c\u0930|\u0938\u093f\u0924?\.?|\u0905\u0915\u094d\u091f\u0942\u092c\u0930|\u0905\u0915\u094d\u091f\u0942\.?|\u0928\u0935\u092e\u094d\u092c\u0930|\u0928\u0935\u0902\u092c\u0930?|\u0926\u093f\u0938\u092e\u094d\u092c\u0930|\u0926\u093f\u0938\u0902\u092c\u0930?)/i,monthsShortStrictRegex:/^(\u091c\u0928\.?|\u092b\u093c\u0930\.?|\u092e\u093e\u0930\u094d\u091a?|\u0905\u092a\u094d\u0930\u0948\.?|\u092e\u0908?|\u091c\u0942\u0928?|\u091c\u0941\u0932\.?|\u0905\u0917\.?|\u0938\u093f\u0924\.?|\u0905\u0915\u094d\u091f\u0942\.?|\u0928\u0935\.?|\u0926\u093f\u0938\.?)/i,calendar:{sameDay:"[\u0906\u091c] LT",nextDay:"[\u0915\u0932] LT",nextWeek:"dddd, LT",lastDay:"[\u0915\u0932] LT",lastWeek:"[\u092a\u093f\u091b\u0932\u0947] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u092e\u0947\u0902",past:"%s \u092a\u0939\u0932\u0947",s:"\u0915\u0941\u091b \u0939\u0940 \u0915\u094d\u0937\u0923",ss:"%d \u0938\u0947\u0915\u0902\u0921",m:"\u090f\u0915 \u092e\u093f\u0928\u091f",mm:"%d \u092e\u093f\u0928\u091f",h:"\u090f\u0915 \u0918\u0902\u091f\u093e",hh:"%d \u0918\u0902\u091f\u0947",d:"\u090f\u0915 \u0926\u093f\u0928",dd:"%d \u0926\u093f\u0928",M:"\u090f\u0915 \u092e\u0939\u0940\u0928\u0947",MM:"%d \u092e\u0939\u0940\u0928\u0947",y:"\u090f\u0915 \u0935\u0930\u094d\u0937",yy:"%d \u0935\u0930\u094d\u0937"},preparse:function(e){return e.replace(/[\u0967\u0968\u0969\u096a\u096b\u096c\u096d\u096e\u096f\u0966]/g,function(e){return Hs[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return Ss[e]})},meridiemParse:/\u0930\u093e\u0924|\u0938\u0941\u092c\u0939|\u0926\u094b\u092a\u0939\u0930|\u0936\u093e\u092e/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0930\u093e\u0924"===a?e<4?e:e+12:"\u0938\u0941\u092c\u0939"===a?e:"\u0926\u094b\u092a\u0939\u0930"===a?10<=e?e:e+12:"\u0936\u093e\u092e"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0930\u093e\u0924":e<10?"\u0938\u0941\u092c\u0939":e<17?"\u0926\u094b\u092a\u0939\u0930":e<20?"\u0936\u093e\u092e":"\u0930\u093e\u0924"},week:{dow:0,doy:6}}),M.defineLocale("hr",{months:{format:"sije\u010dnja_velja\u010de_o\u017eujka_travnja_svibnja_lipnja_srpnja_kolovoza_rujna_listopada_studenoga_prosinca".split("_"),standalone:"sije\u010danj_velja\u010da_o\u017eujak_travanj_svibanj_lipanj_srpanj_kolovoz_rujan_listopad_studeni_prosinac".split("_")},monthsShort:"sij._velj._o\u017eu._tra._svi._lip._srp._kol._ruj._lis._stu._pro.".split("_"),monthsParseExact:!0,weekdays:"nedjelja_ponedjeljak_utorak_srijeda_\u010detvrtak_petak_subota".split("_"),weekdaysShort:"ned._pon._uto._sri._\u010det._pet._sub.".split("_"),weekdaysMin:"ne_po_ut_sr_\u010de_pe_su".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"Do MMMM YYYY",LLL:"Do MMMM YYYY H:mm",LLLL:"dddd, Do MMMM YYYY H:mm"},calendar:{sameDay:"[danas u] LT",nextDay:"[sutra u] LT",nextWeek:function(){switch(this.day()){case 0:return"[u] [nedjelju] [u] LT";case 3:return"[u] [srijedu] [u] LT";case 6:return"[u] [subotu] [u] LT";case 1:case 2:case 4:case 5:return"[u] dddd [u] LT"}},lastDay:"[ju\u010der u] LT",lastWeek:function(){switch(this.day()){case 0:return"[pro\u0161lu] [nedjelju] [u] LT";case 3:return"[pro\u0161lu] [srijedu] [u] LT";case 6:return"[pro\u0161le] [subote] [u] LT";case 1:case 2:case 4:case 5:return"[pro\u0161li] dddd [u] LT"}},sameElse:"L"},relativeTime:{future:"za %s",past:"prije %s",s:"par sekundi",ss:vs,m:vs,mm:vs,h:vs,hh:vs,d:"dan",dd:vs,M:"mjesec",MM:vs,y:"godinu",yy:vs},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}});var js="vas\xe1rnap h\xe9tf\u0151n kedden szerd\xe1n cs\xfct\xf6rt\xf6k\xf6n p\xe9nteken szombaton".split(" ");function xs(e,a,t,s){var n=e;switch(t){case"s":return s||a?"n\xe9h\xe1ny m\xe1sodperc":"n\xe9h\xe1ny m\xe1sodperce";case"ss":return n+(s||a)?" m\xe1sodperc":" m\xe1sodperce";case"m":return"egy"+(s||a?" perc":" perce");case"mm":return n+(s||a?" perc":" perce");case"h":return"egy"+(s||a?" \xf3ra":" \xf3r\xe1ja");case"hh":return n+(s||a?" \xf3ra":" \xf3r\xe1ja");case"d":return"egy"+(s||a?" nap":" napja");case"dd":return n+(s||a?" nap":" napja");case"M":return"egy"+(s||a?" h\xf3nap":" h\xf3napja");case"MM":return n+(s||a?" h\xf3nap":" h\xf3napja");case"y":return"egy"+(s||a?" \xe9v":" \xe9ve");case"yy":return n+(s||a?" \xe9v":" \xe9ve")}return""}function Ps(e){return(e?"":"[m\xfalt] ")+"["+js[this.day()]+"] LT[-kor]"}function Os(e){return e%100==11||e%10!=1}function Ws(e,a,t,s){var n=e+" ";switch(t){case"s":return a||s?"nokkrar sek\xfandur":"nokkrum sek\xfandum";case"ss":return Os(e)?n+(a||s?"sek\xfandur":"sek\xfandum"):n+"sek\xfanda";case"m":return a?"m\xedn\xfata":"m\xedn\xfatu";case"mm":return Os(e)?n+(a||s?"m\xedn\xfatur":"m\xedn\xfatum"):a?n+"m\xedn\xfata":n+"m\xedn\xfatu";case"hh":return Os(e)?n+(a||s?"klukkustundir":"klukkustundum"):n+"klukkustund";case"d":return a?"dagur":s?"dag":"degi";case"dd":return Os(e)?a?n+"dagar":n+(s?"daga":"d\xf6gum"):a?n+"dagur":n+(s?"dag":"degi");case"M":return a?"m\xe1nu\xf0ur":s?"m\xe1nu\xf0":"m\xe1nu\xf0i";case"MM":return Os(e)?a?n+"m\xe1nu\xf0ir":n+(s?"m\xe1nu\xf0i":"m\xe1nu\xf0um"):a?n+"m\xe1nu\xf0ur":n+(s?"m\xe1nu\xf0":"m\xe1nu\xf0i");case"y":return a||s?"\xe1r":"\xe1ri";case"yy":return Os(e)?n+(a||s?"\xe1r":"\xe1rum"):n+(a||s?"\xe1r":"\xe1ri")}}M.defineLocale("hu",{months:"janu\xe1r_febru\xe1r_m\xe1rcius_\xe1prilis_m\xe1jus_j\xfanius_j\xfalius_augusztus_szeptember_okt\xf3ber_november_december".split("_"),monthsShort:"jan._feb._m\xe1rc._\xe1pr._m\xe1j._j\xfan._j\xfal._aug._szept._okt._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"vas\xe1rnap_h\xe9tf\u0151_kedd_szerda_cs\xfct\xf6rt\xf6k_p\xe9ntek_szombat".split("_"),weekdaysShort:"vas_h\xe9t_kedd_sze_cs\xfct_p\xe9n_szo".split("_"),weekdaysMin:"v_h_k_sze_cs_p_szo".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"YYYY.MM.DD.",LL:"YYYY. MMMM D.",LLL:"YYYY. MMMM D. H:mm",LLLL:"YYYY. MMMM D., dddd H:mm"},meridiemParse:/de|du/i,isPM:function(e){return"u"===e.charAt(1).toLowerCase()},meridiem:function(e,a,t){return e<12?!0===t?"de":"DE":!0===t?"du":"DU"},calendar:{sameDay:"[ma] LT[-kor]",nextDay:"[holnap] LT[-kor]",nextWeek:function(){return Ps.call(this,!0)},lastDay:"[tegnap] LT[-kor]",lastWeek:function(){return Ps.call(this,!1)},sameElse:"L"},relativeTime:{future:"%s m\xfalva",past:"%s",s:xs,ss:xs,m:xs,mm:xs,h:xs,hh:xs,d:xs,dd:xs,M:xs,MM:xs,y:xs,yy:xs},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("hy-am",{months:{format:"\u0570\u0578\u0582\u0576\u057e\u0561\u0580\u056b_\u0583\u0565\u057f\u0580\u057e\u0561\u0580\u056b_\u0574\u0561\u0580\u057f\u056b_\u0561\u057a\u0580\u056b\u056c\u056b_\u0574\u0561\u0575\u056b\u057d\u056b_\u0570\u0578\u0582\u0576\u056b\u057d\u056b_\u0570\u0578\u0582\u056c\u056b\u057d\u056b_\u0585\u0563\u0578\u057d\u057f\u0578\u057d\u056b_\u057d\u0565\u057a\u057f\u0565\u0574\u0562\u0565\u0580\u056b_\u0570\u0578\u056f\u057f\u0565\u0574\u0562\u0565\u0580\u056b_\u0576\u0578\u0575\u0565\u0574\u0562\u0565\u0580\u056b_\u0564\u0565\u056f\u057f\u0565\u0574\u0562\u0565\u0580\u056b".split("_"),standalone:"\u0570\u0578\u0582\u0576\u057e\u0561\u0580_\u0583\u0565\u057f\u0580\u057e\u0561\u0580_\u0574\u0561\u0580\u057f_\u0561\u057a\u0580\u056b\u056c_\u0574\u0561\u0575\u056b\u057d_\u0570\u0578\u0582\u0576\u056b\u057d_\u0570\u0578\u0582\u056c\u056b\u057d_\u0585\u0563\u0578\u057d\u057f\u0578\u057d_\u057d\u0565\u057a\u057f\u0565\u0574\u0562\u0565\u0580_\u0570\u0578\u056f\u057f\u0565\u0574\u0562\u0565\u0580_\u0576\u0578\u0575\u0565\u0574\u0562\u0565\u0580_\u0564\u0565\u056f\u057f\u0565\u0574\u0562\u0565\u0580".split("_")},monthsShort:"\u0570\u0576\u057e_\u0583\u057f\u0580_\u0574\u0580\u057f_\u0561\u057a\u0580_\u0574\u0575\u057d_\u0570\u0576\u057d_\u0570\u056c\u057d_\u0585\u0563\u057d_\u057d\u057a\u057f_\u0570\u056f\u057f_\u0576\u0574\u0562_\u0564\u056f\u057f".split("_"),weekdays:"\u056f\u056b\u0580\u0561\u056f\u056b_\u0565\u0580\u056f\u0578\u0582\u0577\u0561\u0562\u0569\u056b_\u0565\u0580\u0565\u0584\u0577\u0561\u0562\u0569\u056b_\u0579\u0578\u0580\u0565\u0584\u0577\u0561\u0562\u0569\u056b_\u0570\u056b\u0576\u0563\u0577\u0561\u0562\u0569\u056b_\u0578\u0582\u0580\u0562\u0561\u0569_\u0577\u0561\u0562\u0561\u0569".split("_"),weekdaysShort:"\u056f\u0580\u056f_\u0565\u0580\u056f_\u0565\u0580\u0584_\u0579\u0580\u0584_\u0570\u0576\u0563_\u0578\u0582\u0580\u0562_\u0577\u0562\u0569".split("_"),weekdaysMin:"\u056f\u0580\u056f_\u0565\u0580\u056f_\u0565\u0580\u0584_\u0579\u0580\u0584_\u0570\u0576\u0563_\u0578\u0582\u0580\u0562_\u0577\u0562\u0569".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY \u0569.",LLL:"D MMMM YYYY \u0569., HH:mm",LLLL:"dddd, D MMMM YYYY \u0569., HH:mm"},calendar:{sameDay:"[\u0561\u0575\u057d\u0585\u0580] LT",nextDay:"[\u057e\u0561\u0572\u0568] LT",lastDay:"[\u0565\u0580\u0565\u056f] LT",nextWeek:function(){return"dddd [\u0585\u0580\u0568 \u056a\u0561\u0574\u0568] LT"},lastWeek:function(){return"[\u0561\u0576\u0581\u0561\u056e] dddd [\u0585\u0580\u0568 \u056a\u0561\u0574\u0568] LT"},sameElse:"L"},relativeTime:{future:"%s \u0570\u0565\u057f\u0578",past:"%s \u0561\u057c\u0561\u057b",s:"\u0574\u056b \u0584\u0561\u0576\u056b \u057e\u0561\u0575\u0580\u056f\u0575\u0561\u0576",ss:"%d \u057e\u0561\u0575\u0580\u056f\u0575\u0561\u0576",m:"\u0580\u0578\u057a\u0565",mm:"%d \u0580\u0578\u057a\u0565",h:"\u056a\u0561\u0574",hh:"%d \u056a\u0561\u0574",d:"\u0585\u0580",dd:"%d \u0585\u0580",M:"\u0561\u0574\u056b\u057d",MM:"%d \u0561\u0574\u056b\u057d",y:"\u057f\u0561\u0580\u056b",yy:"%d \u057f\u0561\u0580\u056b"},meridiemParse:/\u0563\u056b\u0577\u0565\u0580\u057e\u0561|\u0561\u057c\u0561\u057e\u0578\u057f\u057e\u0561|\u0581\u0565\u0580\u0565\u056f\u057e\u0561|\u0565\u0580\u0565\u056f\u0578\u0575\u0561\u0576/,isPM:function(e){return/^(\u0581\u0565\u0580\u0565\u056f\u057e\u0561|\u0565\u0580\u0565\u056f\u0578\u0575\u0561\u0576)$/.test(e)},meridiem:function(e){return e<4?"\u0563\u056b\u0577\u0565\u0580\u057e\u0561":e<12?"\u0561\u057c\u0561\u057e\u0578\u057f\u057e\u0561":e<17?"\u0581\u0565\u0580\u0565\u056f\u057e\u0561":"\u0565\u0580\u0565\u056f\u0578\u0575\u0561\u0576"},dayOfMonthOrdinalParse:/\d{1,2}|\d{1,2}-(\u056b\u0576|\u0580\u0564)/,ordinal:function(e,a){switch(a){case"DDD":case"w":case"W":case"DDDo":return 1===e?e+"-\u056b\u0576":e+"-\u0580\u0564";default:return e}},week:{dow:1,doy:7}}),M.defineLocale("id",{months:"Januari_Februari_Maret_April_Mei_Juni_Juli_Agustus_September_Oktober_November_Desember".split("_"),monthsShort:"Jan_Feb_Mar_Apr_Mei_Jun_Jul_Agt_Sep_Okt_Nov_Des".split("_"),weekdays:"Minggu_Senin_Selasa_Rabu_Kamis_Jumat_Sabtu".split("_"),weekdaysShort:"Min_Sen_Sel_Rab_Kam_Jum_Sab".split("_"),weekdaysMin:"Mg_Sn_Sl_Rb_Km_Jm_Sb".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [pukul] HH.mm",LLLL:"dddd, D MMMM YYYY [pukul] HH.mm"},meridiemParse:/pagi|siang|sore|malam/,meridiemHour:function(e,a){return 12===e&&(e=0),"pagi"===a?e:"siang"===a?11<=e?e:e+12:"sore"===a||"malam"===a?e+12:void 0},meridiem:function(e,a,t){return e<11?"pagi":e<15?"siang":e<19?"sore":"malam"},calendar:{sameDay:"[Hari ini pukul] LT",nextDay:"[Besok pukul] LT",nextWeek:"dddd [pukul] LT",lastDay:"[Kemarin pukul] LT",lastWeek:"dddd [lalu pukul] LT",sameElse:"L"},relativeTime:{future:"dalam %s",past:"%s yang lalu",s:"beberapa detik",ss:"%d detik",m:"semenit",mm:"%d menit",h:"sejam",hh:"%d jam",d:"sehari",dd:"%d hari",M:"sebulan",MM:"%d bulan",y:"setahun",yy:"%d tahun"},week:{dow:0,doy:6}}),M.defineLocale("is",{months:"jan\xfaar_febr\xfaar_mars_apr\xedl_ma\xed_j\xfan\xed_j\xfal\xed_\xe1g\xfast_september_okt\xf3ber_n\xf3vember_desember".split("_"),monthsShort:"jan_feb_mar_apr_ma\xed_j\xfan_j\xfal_\xe1g\xfa_sep_okt_n\xf3v_des".split("_"),weekdays:"sunnudagur_m\xe1nudagur_\xferi\xf0judagur_mi\xf0vikudagur_fimmtudagur_f\xf6studagur_laugardagur".split("_"),weekdaysShort:"sun_m\xe1n_\xferi_mi\xf0_fim_f\xf6s_lau".split("_"),weekdaysMin:"Su_M\xe1_\xder_Mi_Fi_F\xf6_La".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY [kl.] H:mm",LLLL:"dddd, D. MMMM YYYY [kl.] H:mm"},calendar:{sameDay:"[\xed dag kl.] LT",nextDay:"[\xe1 morgun kl.] LT",nextWeek:"dddd [kl.] LT",lastDay:"[\xed g\xe6r kl.] LT",lastWeek:"[s\xed\xf0asta] dddd [kl.] LT",sameElse:"L"},relativeTime:{future:"eftir %s",past:"fyrir %s s\xed\xf0an",s:Ws,ss:Ws,m:Ws,mm:Ws,h:"klukkustund",hh:Ws,d:Ws,dd:Ws,M:Ws,MM:Ws,y:Ws,yy:Ws},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("it-ch",{months:"gennaio_febbraio_marzo_aprile_maggio_giugno_luglio_agosto_settembre_ottobre_novembre_dicembre".split("_"),monthsShort:"gen_feb_mar_apr_mag_giu_lug_ago_set_ott_nov_dic".split("_"),weekdays:"domenica_luned\xec_marted\xec_mercoled\xec_gioved\xec_venerd\xec_sabato".split("_"),weekdaysShort:"dom_lun_mar_mer_gio_ven_sab".split("_"),weekdaysMin:"do_lu_ma_me_gi_ve_sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[Oggi alle] LT",nextDay:"[Domani alle] LT",nextWeek:"dddd [alle] LT",lastDay:"[Ieri alle] LT",lastWeek:function(){switch(this.day()){case 0:return"[la scorsa] dddd [alle] LT";default:return"[lo scorso] dddd [alle] LT"}},sameElse:"L"},relativeTime:{future:function(e){return(/^[0-9].+$/.test(e)?"tra":"in")+" "+e},past:"%s fa",s:"alcuni secondi",ss:"%d secondi",m:"un minuto",mm:"%d minuti",h:"un'ora",hh:"%d ore",d:"un giorno",dd:"%d giorni",M:"un mese",MM:"%d mesi",y:"un anno",yy:"%d anni"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),M.defineLocale("it",{months:"gennaio_febbraio_marzo_aprile_maggio_giugno_luglio_agosto_settembre_ottobre_novembre_dicembre".split("_"),monthsShort:"gen_feb_mar_apr_mag_giu_lug_ago_set_ott_nov_dic".split("_"),weekdays:"domenica_luned\xec_marted\xec_mercoled\xec_gioved\xec_venerd\xec_sabato".split("_"),weekdaysShort:"dom_lun_mar_mer_gio_ven_sab".split("_"),weekdaysMin:"do_lu_ma_me_gi_ve_sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:function(){return"[Oggi a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT"},nextDay:function(){return"[Domani a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT"},nextWeek:function(){return"dddd [a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT"},lastDay:function(){return"[Ieri a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT"},lastWeek:function(){switch(this.day()){case 0:return"[La scorsa] dddd [a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT";default:return"[Lo scorso] dddd [a"+(1<this.hours()?"lle ":0===this.hours()?" ":"ll'")+"]LT"}},sameElse:"L"},relativeTime:{future:"tra %s",past:"%s fa",s:"alcuni secondi",ss:"%d secondi",m:"un minuto",mm:"%d minuti",h:"un'ora",hh:"%d ore",d:"un giorno",dd:"%d giorni",w:"una settimana",ww:"%d settimane",M:"un mese",MM:"%d mesi",y:"un anno",yy:"%d anni"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),M.defineLocale("ja",{eras:[{since:"2019-05-01",offset:1,name:"\u4ee4\u548c",narrow:"\u32ff",abbr:"R"},{since:"1989-01-08",until:"2019-04-30",offset:1,name:"\u5e73\u6210",narrow:"\u337b",abbr:"H"},{since:"1926-12-25",until:"1989-01-07",offset:1,name:"\u662d\u548c",narrow:"\u337c",abbr:"S"},{since:"1912-07-30",until:"1926-12-24",offset:1,name:"\u5927\u6b63",narrow:"\u337d",abbr:"T"},{since:"1873-01-01",until:"1912-07-29",offset:6,name:"\u660e\u6cbb",narrow:"\u337e",abbr:"M"},{since:"0001-01-01",until:"1873-12-31",offset:1,name:"\u897f\u66a6",narrow:"AD",abbr:"AD"},{since:"0000-12-31",until:-1/0,offset:1,name:"\u7d00\u5143\u524d",narrow:"BC",abbr:"BC"}],eraYearOrdinalRegex:/(\u5143|\d+)\u5e74/,eraYearOrdinalParse:function(e,a){return"\u5143"===a[1]?1:parseInt(a[1]||e,10)},months:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),monthsShort:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),weekdays:"\u65e5\u66dc\u65e5_\u6708\u66dc\u65e5_\u706b\u66dc\u65e5_\u6c34\u66dc\u65e5_\u6728\u66dc\u65e5_\u91d1\u66dc\u65e5_\u571f\u66dc\u65e5".split("_"),weekdaysShort:"\u65e5_\u6708_\u706b_\u6c34_\u6728_\u91d1_\u571f".split("_"),weekdaysMin:"\u65e5_\u6708_\u706b_\u6c34_\u6728_\u91d1_\u571f".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY/MM/DD",LL:"YYYY\u5e74M\u6708D\u65e5",LLL:"YYYY\u5e74M\u6708D\u65e5 HH:mm",LLLL:"YYYY\u5e74M\u6708D\u65e5 dddd HH:mm",l:"YYYY/MM/DD",ll:"YYYY\u5e74M\u6708D\u65e5",lll:"YYYY\u5e74M\u6708D\u65e5 HH:mm",llll:"YYYY\u5e74M\u6708D\u65e5(ddd) HH:mm"},meridiemParse:/\u5348\u524d|\u5348\u5f8c/i,isPM:function(e){return"\u5348\u5f8c"===e},meridiem:function(e,a,t){return e<12?"\u5348\u524d":"\u5348\u5f8c"},calendar:{sameDay:"[\u4eca\u65e5] LT",nextDay:"[\u660e\u65e5] LT",nextWeek:function(e){return e.week()!==this.week()?"[\u6765\u9031]dddd LT":"dddd LT"},lastDay:"[\u6628\u65e5] LT",lastWeek:function(e){return this.week()!==e.week()?"[\u5148\u9031]dddd LT":"dddd LT"},sameElse:"L"},dayOfMonthOrdinalParse:/\d{1,2}\u65e5/,ordinal:function(e,a){switch(a){case"y":return 1===e?"\u5143\u5e74":e+"\u5e74";case"d":case"D":case"DDD":return e+"\u65e5";default:return e}},relativeTime:{future:"%s\u5f8c",past:"%s\u524d",s:"\u6570\u79d2",ss:"%d\u79d2",m:"1\u5206",mm:"%d\u5206",h:"1\u6642\u9593",hh:"%d\u6642\u9593",d:"1\u65e5",dd:"%d\u65e5",M:"1\u30f6\u6708",MM:"%d\u30f6\u6708",y:"1\u5e74",yy:"%d\u5e74"}}),M.defineLocale("jv",{months:"Januari_Februari_Maret_April_Mei_Juni_Juli_Agustus_September_Oktober_Nopember_Desember".split("_"),monthsShort:"Jan_Feb_Mar_Apr_Mei_Jun_Jul_Ags_Sep_Okt_Nop_Des".split("_"),weekdays:"Minggu_Senen_Seloso_Rebu_Kemis_Jemuwah_Septu".split("_"),weekdaysShort:"Min_Sen_Sel_Reb_Kem_Jem_Sep".split("_"),weekdaysMin:"Mg_Sn_Sl_Rb_Km_Jm_Sp".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [pukul] HH.mm",LLLL:"dddd, D MMMM YYYY [pukul] HH.mm"},meridiemParse:/enjing|siyang|sonten|ndalu/,meridiemHour:function(e,a){return 12===e&&(e=0),"enjing"===a?e:"siyang"===a?11<=e?e:e+12:"sonten"===a||"ndalu"===a?e+12:void 0},meridiem:function(e,a,t){return e<11?"enjing":e<15?"siyang":e<19?"sonten":"ndalu"},calendar:{sameDay:"[Dinten puniko pukul] LT",nextDay:"[Mbenjang pukul] LT",nextWeek:"dddd [pukul] LT",lastDay:"[Kala wingi pukul] LT",lastWeek:"dddd [kepengker pukul] LT",sameElse:"L"},relativeTime:{future:"wonten ing %s",past:"%s ingkang kepengker",s:"sawetawis detik",ss:"%d detik",m:"setunggal menit",mm:"%d menit",h:"setunggal jam",hh:"%d jam",d:"sedinten",dd:"%d dinten",M:"sewulan",MM:"%d wulan",y:"setaun",yy:"%d taun"},week:{dow:1,doy:7}}),M.defineLocale("ka",{months:"\u10d8\u10d0\u10dc\u10d5\u10d0\u10e0\u10d8_\u10d7\u10d4\u10d1\u10d4\u10e0\u10d5\u10d0\u10da\u10d8_\u10db\u10d0\u10e0\u10e2\u10d8_\u10d0\u10de\u10e0\u10d8\u10da\u10d8_\u10db\u10d0\u10d8\u10e1\u10d8_\u10d8\u10d5\u10dc\u10d8\u10e1\u10d8_\u10d8\u10d5\u10da\u10d8\u10e1\u10d8_\u10d0\u10d2\u10d5\u10d8\u10e1\u10e2\u10dd_\u10e1\u10d4\u10e5\u10e2\u10d4\u10db\u10d1\u10d4\u10e0\u10d8_\u10dd\u10e5\u10e2\u10dd\u10db\u10d1\u10d4\u10e0\u10d8_\u10dc\u10dd\u10d4\u10db\u10d1\u10d4\u10e0\u10d8_\u10d3\u10d4\u10d9\u10d4\u10db\u10d1\u10d4\u10e0\u10d8".split("_"),monthsShort:"\u10d8\u10d0\u10dc_\u10d7\u10d4\u10d1_\u10db\u10d0\u10e0_\u10d0\u10de\u10e0_\u10db\u10d0\u10d8_\u10d8\u10d5\u10dc_\u10d8\u10d5\u10da_\u10d0\u10d2\u10d5_\u10e1\u10d4\u10e5_\u10dd\u10e5\u10e2_\u10dc\u10dd\u10d4_\u10d3\u10d4\u10d9".split("_"),weekdays:{standalone:"\u10d9\u10d5\u10d8\u10e0\u10d0_\u10dd\u10e0\u10e8\u10d0\u10d1\u10d0\u10d7\u10d8_\u10e1\u10d0\u10db\u10e8\u10d0\u10d1\u10d0\u10d7\u10d8_\u10dd\u10d7\u10ee\u10e8\u10d0\u10d1\u10d0\u10d7\u10d8_\u10ee\u10e3\u10d7\u10e8\u10d0\u10d1\u10d0\u10d7\u10d8_\u10de\u10d0\u10e0\u10d0\u10e1\u10d9\u10d4\u10d5\u10d8_\u10e8\u10d0\u10d1\u10d0\u10d7\u10d8".split("_"),format:"\u10d9\u10d5\u10d8\u10e0\u10d0\u10e1_\u10dd\u10e0\u10e8\u10d0\u10d1\u10d0\u10d7\u10e1_\u10e1\u10d0\u10db\u10e8\u10d0\u10d1\u10d0\u10d7\u10e1_\u10dd\u10d7\u10ee\u10e8\u10d0\u10d1\u10d0\u10d7\u10e1_\u10ee\u10e3\u10d7\u10e8\u10d0\u10d1\u10d0\u10d7\u10e1_\u10de\u10d0\u10e0\u10d0\u10e1\u10d9\u10d4\u10d5\u10e1_\u10e8\u10d0\u10d1\u10d0\u10d7\u10e1".split("_"),isFormat:/(\u10ec\u10d8\u10dc\u10d0|\u10e8\u10d4\u10db\u10d3\u10d4\u10d2)/},weekdaysShort:"\u10d9\u10d5\u10d8_\u10dd\u10e0\u10e8_\u10e1\u10d0\u10db_\u10dd\u10d7\u10ee_\u10ee\u10e3\u10d7_\u10de\u10d0\u10e0_\u10e8\u10d0\u10d1".split("_"),weekdaysMin:"\u10d9\u10d5_\u10dd\u10e0_\u10e1\u10d0_\u10dd\u10d7_\u10ee\u10e3_\u10de\u10d0_\u10e8\u10d0".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u10d3\u10e6\u10d4\u10e1] LT[-\u10d6\u10d4]",nextDay:"[\u10ee\u10d5\u10d0\u10da] LT[-\u10d6\u10d4]",lastDay:"[\u10d2\u10e3\u10e8\u10d8\u10dc] LT[-\u10d6\u10d4]",nextWeek:"[\u10e8\u10d4\u10db\u10d3\u10d4\u10d2] dddd LT[-\u10d6\u10d4]",lastWeek:"[\u10ec\u10d8\u10dc\u10d0] dddd LT-\u10d6\u10d4",sameElse:"L"},relativeTime:{future:function(e){return e.replace(/(\u10ec\u10d0\u10db|\u10ec\u10e3\u10d7|\u10e1\u10d0\u10d0\u10d7|\u10ec\u10d4\u10da|\u10d3\u10e6|\u10d7\u10d5)(\u10d8|\u10d4)/,function(e,a,t){return"\u10d8"===t?a+"\u10e8\u10d8":a+t+"\u10e8\u10d8"})},past:function(e){return/(\u10ec\u10d0\u10db\u10d8|\u10ec\u10e3\u10d7\u10d8|\u10e1\u10d0\u10d0\u10d7\u10d8|\u10d3\u10e6\u10d4|\u10d7\u10d5\u10d4)/.test(e)?e.replace(/(\u10d8|\u10d4)$/,"\u10d8\u10e1 \u10ec\u10d8\u10dc"):/\u10ec\u10d4\u10da\u10d8/.test(e)?e.replace(/\u10ec\u10d4\u10da\u10d8$/,"\u10ec\u10da\u10d8\u10e1 \u10ec\u10d8\u10dc"):e},s:"\u10e0\u10d0\u10db\u10d3\u10d4\u10dc\u10d8\u10db\u10d4 \u10ec\u10d0\u10db\u10d8",ss:"%d \u10ec\u10d0\u10db\u10d8",m:"\u10ec\u10e3\u10d7\u10d8",mm:"%d \u10ec\u10e3\u10d7\u10d8",h:"\u10e1\u10d0\u10d0\u10d7\u10d8",hh:"%d \u10e1\u10d0\u10d0\u10d7\u10d8",d:"\u10d3\u10e6\u10d4",dd:"%d \u10d3\u10e6\u10d4",M:"\u10d7\u10d5\u10d4",MM:"%d \u10d7\u10d5\u10d4",y:"\u10ec\u10d4\u10da\u10d8",yy:"%d \u10ec\u10d4\u10da\u10d8"},dayOfMonthOrdinalParse:/0|1-\u10da\u10d8|\u10db\u10d4-\d{1,2}|\d{1,2}-\u10d4/,ordinal:function(e){return 0===e?e:1===e?e+"-\u10da\u10d8":e<20||e<=100&&e%20==0||e%100==0?"\u10db\u10d4-"+e:e+"-\u10d4"},week:{dow:1,doy:7}});var As={0:"-\u0448\u0456",1:"-\u0448\u0456",2:"-\u0448\u0456",3:"-\u0448\u0456",4:"-\u0448\u0456",5:"-\u0448\u0456",6:"-\u0448\u044b",7:"-\u0448\u0456",8:"-\u0448\u0456",9:"-\u0448\u044b",10:"-\u0448\u044b",20:"-\u0448\u044b",30:"-\u0448\u044b",40:"-\u0448\u044b",50:"-\u0448\u0456",60:"-\u0448\u044b",70:"-\u0448\u0456",80:"-\u0448\u0456",90:"-\u0448\u044b",100:"-\u0448\u0456"},Es=(M.defineLocale("kk",{months:"\u049b\u0430\u04a3\u0442\u0430\u0440_\u0430\u049b\u043f\u0430\u043d_\u043d\u0430\u0443\u0440\u044b\u0437_\u0441\u04d9\u0443\u0456\u0440_\u043c\u0430\u043c\u044b\u0440_\u043c\u0430\u0443\u0441\u044b\u043c_\u0448\u0456\u043b\u0434\u0435_\u0442\u0430\u043c\u044b\u0437_\u049b\u044b\u0440\u043a\u04af\u0439\u0435\u043a_\u049b\u0430\u0437\u0430\u043d_\u049b\u0430\u0440\u0430\u0448\u0430_\u0436\u0435\u043b\u0442\u043e\u049b\u0441\u0430\u043d".split("_"),monthsShort:"\u049b\u0430\u04a3_\u0430\u049b\u043f_\u043d\u0430\u0443_\u0441\u04d9\u0443_\u043c\u0430\u043c_\u043c\u0430\u0443_\u0448\u0456\u043b_\u0442\u0430\u043c_\u049b\u044b\u0440_\u049b\u0430\u0437_\u049b\u0430\u0440_\u0436\u0435\u043b".split("_"),weekdays:"\u0436\u0435\u043a\u0441\u0435\u043d\u0431\u0456_\u0434\u04af\u0439\u0441\u0435\u043d\u0431\u0456_\u0441\u0435\u0439\u0441\u0435\u043d\u0431\u0456_\u0441\u04d9\u0440\u0441\u0435\u043d\u0431\u0456_\u0431\u0435\u0439\u0441\u0435\u043d\u0431\u0456_\u0436\u04b1\u043c\u0430_\u0441\u0435\u043d\u0431\u0456".split("_"),weekdaysShort:"\u0436\u0435\u043a_\u0434\u04af\u0439_\u0441\u0435\u0439_\u0441\u04d9\u0440_\u0431\u0435\u0439_\u0436\u04b1\u043c_\u0441\u0435\u043d".split("_"),weekdaysMin:"\u0436\u043a_\u0434\u0439_\u0441\u0439_\u0441\u0440_\u0431\u0439_\u0436\u043c_\u0441\u043d".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0411\u04af\u0433\u0456\u043d \u0441\u0430\u0493\u0430\u0442] LT",nextDay:"[\u0415\u0440\u0442\u0435\u04a3 \u0441\u0430\u0493\u0430\u0442] LT",nextWeek:"dddd [\u0441\u0430\u0493\u0430\u0442] LT",lastDay:"[\u041a\u0435\u0448\u0435 \u0441\u0430\u0493\u0430\u0442] LT",lastWeek:"[\u04e8\u0442\u043a\u0435\u043d \u0430\u043f\u0442\u0430\u043d\u044b\u04a3] dddd [\u0441\u0430\u0493\u0430\u0442] LT",sameElse:"L"},relativeTime:{future:"%s \u0456\u0448\u0456\u043d\u0434\u0435",past:"%s \u0431\u04b1\u0440\u044b\u043d",s:"\u0431\u0456\u0440\u043d\u0435\u0448\u0435 \u0441\u0435\u043a\u0443\u043d\u0434",ss:"%d \u0441\u0435\u043a\u0443\u043d\u0434",m:"\u0431\u0456\u0440 \u043c\u0438\u043d\u0443\u0442",mm:"%d \u043c\u0438\u043d\u0443\u0442",h:"\u0431\u0456\u0440 \u0441\u0430\u0493\u0430\u0442",hh:"%d \u0441\u0430\u0493\u0430\u0442",d:"\u0431\u0456\u0440 \u043a\u04af\u043d",dd:"%d \u043a\u04af\u043d",M:"\u0431\u0456\u0440 \u0430\u0439",MM:"%d \u0430\u0439",y:"\u0431\u0456\u0440 \u0436\u044b\u043b",yy:"%d \u0436\u044b\u043b"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0448\u0456|\u0448\u044b)/,ordinal:function(e){return e+(As[e]||As[e%10]||As[100<=e?100:null])},week:{dow:1,doy:7}}),{1:"\u17e1",2:"\u17e2",3:"\u17e3",4:"\u17e4",5:"\u17e5",6:"\u17e6",7:"\u17e7",8:"\u17e8",9:"\u17e9",0:"\u17e0"}),Fs={"\u17e1":"1","\u17e2":"2","\u17e3":"3","\u17e4":"4","\u17e5":"5","\u17e6":"6","\u17e7":"7","\u17e8":"8","\u17e9":"9","\u17e0":"0"},zs=(M.defineLocale("km",{months:"\u1798\u1780\u179a\u17b6_\u1780\u17bb\u1798\u17d2\u1797\u17c8_\u1798\u17b8\u1793\u17b6_\u1798\u17c1\u179f\u17b6_\u17a7\u179f\u1797\u17b6_\u1798\u17b7\u1790\u17bb\u1793\u17b6_\u1780\u1780\u17d2\u1780\u178a\u17b6_\u179f\u17b8\u17a0\u17b6_\u1780\u1789\u17d2\u1789\u17b6_\u178f\u17bb\u179b\u17b6_\u179c\u17b7\u1785\u17d2\u1786\u17b7\u1780\u17b6_\u1792\u17d2\u1793\u17bc".split("_"),monthsShort:"\u1798\u1780\u179a\u17b6_\u1780\u17bb\u1798\u17d2\u1797\u17c8_\u1798\u17b8\u1793\u17b6_\u1798\u17c1\u179f\u17b6_\u17a7\u179f\u1797\u17b6_\u1798\u17b7\u1790\u17bb\u1793\u17b6_\u1780\u1780\u17d2\u1780\u178a\u17b6_\u179f\u17b8\u17a0\u17b6_\u1780\u1789\u17d2\u1789\u17b6_\u178f\u17bb\u179b\u17b6_\u179c\u17b7\u1785\u17d2\u1786\u17b7\u1780\u17b6_\u1792\u17d2\u1793\u17bc".split("_"),weekdays:"\u17a2\u17b6\u1791\u17b7\u178f\u17d2\u1799_\u1785\u17d0\u1793\u17d2\u1791_\u17a2\u1784\u17d2\u1782\u17b6\u179a_\u1796\u17bb\u1792_\u1796\u17d2\u179a\u17a0\u179f\u17d2\u1794\u178f\u17b7\u17cd_\u179f\u17bb\u1780\u17d2\u179a_\u179f\u17c5\u179a\u17cd".split("_"),weekdaysShort:"\u17a2\u17b6_\u1785_\u17a2_\u1796_\u1796\u17d2\u179a_\u179f\u17bb_\u179f".split("_"),weekdaysMin:"\u17a2\u17b6_\u1785_\u17a2_\u1796_\u1796\u17d2\u179a_\u179f\u17bb_\u179f".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},meridiemParse:/\u1796\u17d2\u179a\u17b9\u1780|\u179b\u17d2\u1784\u17b6\u1785/,isPM:function(e){return"\u179b\u17d2\u1784\u17b6\u1785"===e},meridiem:function(e,a,t){return e<12?"\u1796\u17d2\u179a\u17b9\u1780":"\u179b\u17d2\u1784\u17b6\u1785"},calendar:{sameDay:"[\u1790\u17d2\u1784\u17c3\u1793\u17c1\u17c7 \u1798\u17c9\u17c4\u1784] LT",nextDay:"[\u179f\u17d2\u17a2\u17c2\u1780 \u1798\u17c9\u17c4\u1784] LT",nextWeek:"dddd [\u1798\u17c9\u17c4\u1784] LT",lastDay:"[\u1798\u17d2\u179f\u17b7\u179b\u1798\u17b7\u1789 \u1798\u17c9\u17c4\u1784] LT",lastWeek:"dddd [\u179f\u1794\u17d2\u178f\u17b6\u17a0\u17cd\u1798\u17bb\u1793] [\u1798\u17c9\u17c4\u1784] LT",sameElse:"L"},relativeTime:{future:"%s\u1791\u17c0\u178f",past:"%s\u1798\u17bb\u1793",s:"\u1794\u17c9\u17bb\u1793\u17d2\u1798\u17b6\u1793\u179c\u17b7\u1793\u17b6\u1791\u17b8",ss:"%d \u179c\u17b7\u1793\u17b6\u1791\u17b8",m:"\u1798\u17bd\u1799\u1793\u17b6\u1791\u17b8",mm:"%d \u1793\u17b6\u1791\u17b8",h:"\u1798\u17bd\u1799\u1798\u17c9\u17c4\u1784",hh:"%d \u1798\u17c9\u17c4\u1784",d:"\u1798\u17bd\u1799\u1790\u17d2\u1784\u17c3",dd:"%d \u1790\u17d2\u1784\u17c3",M:"\u1798\u17bd\u1799\u1781\u17c2",MM:"%d \u1781\u17c2",y:"\u1798\u17bd\u1799\u1786\u17d2\u1793\u17b6\u17c6",yy:"%d \u1786\u17d2\u1793\u17b6\u17c6"},dayOfMonthOrdinalParse:/\u1791\u17b8\d{1,2}/,ordinal:"\u1791\u17b8%d",preparse:function(e){return e.replace(/[\u17e1\u17e2\u17e3\u17e4\u17e5\u17e6\u17e7\u17e8\u17e9\u17e0]/g,function(e){return Fs[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return Es[e]})},week:{dow:1,doy:4}}),{1:"\u0ce7",2:"\u0ce8",3:"\u0ce9",4:"\u0cea",5:"\u0ceb",6:"\u0cec",7:"\u0ced",8:"\u0cee",9:"\u0cef",0:"\u0ce6"}),Ns={"\u0ce7":"1","\u0ce8":"2","\u0ce9":"3","\u0cea":"4","\u0ceb":"5","\u0cec":"6","\u0ced":"7","\u0cee":"8","\u0cef":"9","\u0ce6":"0"},Js=(M.defineLocale("kn",{months:"\u0c9c\u0ca8\u0cb5\u0cb0\u0cbf_\u0cab\u0cc6\u0cac\u0ccd\u0cb0\u0cb5\u0cb0\u0cbf_\u0cae\u0cbe\u0cb0\u0ccd\u0c9a\u0ccd_\u0c8f\u0caa\u0ccd\u0cb0\u0cbf\u0cb2\u0ccd_\u0cae\u0cc6\u0cd5_\u0c9c\u0cc2\u0ca8\u0ccd_\u0c9c\u0cc1\u0cb2\u0cc6\u0cd6_\u0c86\u0c97\u0cb8\u0ccd\u0c9f\u0ccd_\u0cb8\u0cc6\u0caa\u0ccd\u0c9f\u0cc6\u0c82\u0cac\u0cb0\u0ccd_\u0c85\u0c95\u0ccd\u0c9f\u0cc6\u0cc2\u0cd5\u0cac\u0cb0\u0ccd_\u0ca8\u0cb5\u0cc6\u0c82\u0cac\u0cb0\u0ccd_\u0ca1\u0cbf\u0cb8\u0cc6\u0c82\u0cac\u0cb0\u0ccd".split("_"),monthsShort:"\u0c9c\u0ca8_\u0cab\u0cc6\u0cac\u0ccd\u0cb0_\u0cae\u0cbe\u0cb0\u0ccd\u0c9a\u0ccd_\u0c8f\u0caa\u0ccd\u0cb0\u0cbf\u0cb2\u0ccd_\u0cae\u0cc6\u0cd5_\u0c9c\u0cc2\u0ca8\u0ccd_\u0c9c\u0cc1\u0cb2\u0cc6\u0cd6_\u0c86\u0c97\u0cb8\u0ccd\u0c9f\u0ccd_\u0cb8\u0cc6\u0caa\u0ccd\u0c9f\u0cc6\u0c82_\u0c85\u0c95\u0ccd\u0c9f\u0cc6\u0cc2\u0cd5_\u0ca8\u0cb5\u0cc6\u0c82_\u0ca1\u0cbf\u0cb8\u0cc6\u0c82".split("_"),monthsParseExact:!0,weekdays:"\u0cad\u0cbe\u0ca8\u0cc1\u0cb5\u0cbe\u0cb0_\u0cb8\u0cc6\u0cc2\u0cd5\u0cae\u0cb5\u0cbe\u0cb0_\u0cae\u0c82\u0c97\u0cb3\u0cb5\u0cbe\u0cb0_\u0cac\u0cc1\u0ca7\u0cb5\u0cbe\u0cb0_\u0c97\u0cc1\u0cb0\u0cc1\u0cb5\u0cbe\u0cb0_\u0cb6\u0cc1\u0c95\u0ccd\u0cb0\u0cb5\u0cbe\u0cb0_\u0cb6\u0ca8\u0cbf\u0cb5\u0cbe\u0cb0".split("_"),weekdaysShort:"\u0cad\u0cbe\u0ca8\u0cc1_\u0cb8\u0cc6\u0cc2\u0cd5\u0cae_\u0cae\u0c82\u0c97\u0cb3_\u0cac\u0cc1\u0ca7_\u0c97\u0cc1\u0cb0\u0cc1_\u0cb6\u0cc1\u0c95\u0ccd\u0cb0_\u0cb6\u0ca8\u0cbf".split("_"),weekdaysMin:"\u0cad\u0cbe_\u0cb8\u0cc6\u0cc2\u0cd5_\u0cae\u0c82_\u0cac\u0cc1_\u0c97\u0cc1_\u0cb6\u0cc1_\u0cb6".split("_"),longDateFormat:{LT:"A h:mm",LTS:"A h:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm",LLLL:"dddd, D MMMM YYYY, A h:mm"},calendar:{sameDay:"[\u0c87\u0c82\u0ca6\u0cc1] LT",nextDay:"[\u0ca8\u0cbe\u0cb3\u0cc6] LT",nextWeek:"dddd, LT",lastDay:"[\u0ca8\u0cbf\u0ca8\u0ccd\u0ca8\u0cc6] LT",lastWeek:"[\u0c95\u0cc6\u0cc2\u0ca8\u0cc6\u0caf] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0ca8\u0c82\u0ca4\u0cb0",past:"%s \u0cb9\u0cbf\u0c82\u0ca6\u0cc6",s:"\u0c95\u0cc6\u0cb2\u0cb5\u0cc1 \u0c95\u0ccd\u0cb7\u0ca3\u0c97\u0cb3\u0cc1",ss:"%d \u0cb8\u0cc6\u0c95\u0cc6\u0c82\u0ca1\u0cc1\u0c97\u0cb3\u0cc1",m:"\u0c92\u0c82\u0ca6\u0cc1 \u0ca8\u0cbf\u0cae\u0cbf\u0cb7",mm:"%d \u0ca8\u0cbf\u0cae\u0cbf\u0cb7",h:"\u0c92\u0c82\u0ca6\u0cc1 \u0c97\u0c82\u0c9f\u0cc6",hh:"%d \u0c97\u0c82\u0c9f\u0cc6",d:"\u0c92\u0c82\u0ca6\u0cc1 \u0ca6\u0cbf\u0ca8",dd:"%d \u0ca6\u0cbf\u0ca8",M:"\u0c92\u0c82\u0ca6\u0cc1 \u0ca4\u0cbf\u0c82\u0c97\u0cb3\u0cc1",MM:"%d \u0ca4\u0cbf\u0c82\u0c97\u0cb3\u0cc1",y:"\u0c92\u0c82\u0ca6\u0cc1 \u0cb5\u0cb0\u0ccd\u0cb7",yy:"%d \u0cb5\u0cb0\u0ccd\u0cb7"},preparse:function(e){return e.replace(/[\u0ce7\u0ce8\u0ce9\u0cea\u0ceb\u0cec\u0ced\u0cee\u0cef\u0ce6]/g,function(e){return Ns[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return zs[e]})},meridiemParse:/\u0cb0\u0cbe\u0ca4\u0ccd\u0cb0\u0cbf|\u0cac\u0cc6\u0cb3\u0cbf\u0c97\u0ccd\u0c97\u0cc6|\u0cae\u0ca7\u0ccd\u0caf\u0cbe\u0cb9\u0ccd\u0ca8|\u0cb8\u0c82\u0c9c\u0cc6/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0cb0\u0cbe\u0ca4\u0ccd\u0cb0\u0cbf"===a?e<4?e:e+12:"\u0cac\u0cc6\u0cb3\u0cbf\u0c97\u0ccd\u0c97\u0cc6"===a?e:"\u0cae\u0ca7\u0ccd\u0caf\u0cbe\u0cb9\u0ccd\u0ca8"===a?10<=e?e:e+12:"\u0cb8\u0c82\u0c9c\u0cc6"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0cb0\u0cbe\u0ca4\u0ccd\u0cb0\u0cbf":e<10?"\u0cac\u0cc6\u0cb3\u0cbf\u0c97\u0ccd\u0c97\u0cc6":e<17?"\u0cae\u0ca7\u0ccd\u0caf\u0cbe\u0cb9\u0ccd\u0ca8":e<20?"\u0cb8\u0c82\u0c9c\u0cc6":"\u0cb0\u0cbe\u0ca4\u0ccd\u0cb0\u0cbf"},dayOfMonthOrdinalParse:/\d{1,2}(\u0ca8\u0cc6\u0cd5)/,ordinal:function(e){return e+"\u0ca8\u0cc6\u0cd5"},week:{dow:0,doy:6}}),M.defineLocale("ko",{months:"1\uc6d4_2\uc6d4_3\uc6d4_4\uc6d4_5\uc6d4_6\uc6d4_7\uc6d4_8\uc6d4_9\uc6d4_10\uc6d4_11\uc6d4_12\uc6d4".split("_"),monthsShort:"1\uc6d4_2\uc6d4_3\uc6d4_4\uc6d4_5\uc6d4_6\uc6d4_7\uc6d4_8\uc6d4_9\uc6d4_10\uc6d4_11\uc6d4_12\uc6d4".split("_"),weekdays:"\uc77c\uc694\uc77c_\uc6d4\uc694\uc77c_\ud654\uc694\uc77c_\uc218\uc694\uc77c_\ubaa9\uc694\uc77c_\uae08\uc694\uc77c_\ud1a0\uc694\uc77c".split("_"),weekdaysShort:"\uc77c_\uc6d4_\ud654_\uc218_\ubaa9_\uae08_\ud1a0".split("_"),weekdaysMin:"\uc77c_\uc6d4_\ud654_\uc218_\ubaa9_\uae08_\ud1a0".split("_"),longDateFormat:{LT:"A h:mm",LTS:"A h:mm:ss",L:"YYYY.MM.DD.",LL:"YYYY\ub144 MMMM D\uc77c",LLL:"YYYY\ub144 MMMM D\uc77c A h:mm",LLLL:"YYYY\ub144 MMMM D\uc77c dddd A h:mm",l:"YYYY.MM.DD.",ll:"YYYY\ub144 MMMM D\uc77c",lll:"YYYY\ub144 MMMM D\uc77c A h:mm",llll:"YYYY\ub144 MMMM D\uc77c dddd A h:mm"},calendar:{sameDay:"\uc624\ub298 LT",nextDay:"\ub0b4\uc77c LT",nextWeek:"dddd LT",lastDay:"\uc5b4\uc81c LT",lastWeek:"\uc9c0\ub09c\uc8fc dddd LT",sameElse:"L"},relativeTime:{future:"%s \ud6c4",past:"%s \uc804",s:"\uba87 \ucd08",ss:"%d\ucd08",m:"1\ubd84",mm:"%d\ubd84",h:"\ud55c \uc2dc\uac04",hh:"%d\uc2dc\uac04",d:"\ud558\ub8e8",dd:"%d\uc77c",M:"\ud55c \ub2ec",MM:"%d\ub2ec",y:"\uc77c \ub144",yy:"%d\ub144"},dayOfMonthOrdinalParse:/\d{1,2}(\uc77c|\uc6d4|\uc8fc)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"\uc77c";case"M":return e+"\uc6d4";case"w":case"W":return e+"\uc8fc";default:return e}},meridiemParse:/\uc624\uc804|\uc624\ud6c4/,isPM:function(e){return"\uc624\ud6c4"===e},meridiem:function(e,a,t){return e<12?"\uc624\uc804":"\uc624\ud6c4"}}),{1:"\u0661",2:"\u0662",3:"\u0663",4:"\u0664",5:"\u0665",6:"\u0666",7:"\u0667",8:"\u0668",9:"\u0669",0:"\u0660"}),Rs={"\u0661":"1","\u0662":"2","\u0663":"3","\u0664":"4","\u0665":"5","\u0666":"6","\u0667":"7","\u0668":"8","\u0669":"9","\u0660":"0"},ta=["\u06a9\u0627\u0646\u0648\u0646\u06cc \u062f\u0648\u0648\u06d5\u0645","\u0634\u0648\u0628\u0627\u062a","\u0626\u0627\u0632\u0627\u0631","\u0646\u06cc\u0633\u0627\u0646","\u0626\u0627\u06cc\u0627\u0631","\u062d\u0648\u0632\u06d5\u06cc\u0631\u0627\u0646","\u062a\u06d5\u0645\u0645\u0648\u0632","\u0626\u0627\u0628","\u0626\u06d5\u06cc\u0644\u0648\u0648\u0644","\u062a\u0634\u0631\u06cc\u0646\u06cc \u06cc\u06d5\u0643\u06d5\u0645","\u062a\u0634\u0631\u06cc\u0646\u06cc \u062f\u0648\u0648\u06d5\u0645","\u0643\u0627\u0646\u0648\u0646\u06cc \u06cc\u06d5\u06a9\u06d5\u0645"],Cs=(M.defineLocale("ku",{months:ta,monthsShort:ta,weekdays:"\u06cc\u0647\u200c\u0643\u0634\u0647\u200c\u0645\u0645\u0647\u200c_\u062f\u0648\u0648\u0634\u0647\u200c\u0645\u0645\u0647\u200c_\u0633\u06ce\u0634\u0647\u200c\u0645\u0645\u0647\u200c_\u0686\u0648\u0627\u0631\u0634\u0647\u200c\u0645\u0645\u0647\u200c_\u067e\u06ce\u0646\u062c\u0634\u0647\u200c\u0645\u0645\u0647\u200c_\u0647\u0647\u200c\u06cc\u0646\u06cc_\u0634\u0647\u200c\u0645\u0645\u0647\u200c".split("_"),weekdaysShort:"\u06cc\u0647\u200c\u0643\u0634\u0647\u200c\u0645_\u062f\u0648\u0648\u0634\u0647\u200c\u0645_\u0633\u06ce\u0634\u0647\u200c\u0645_\u0686\u0648\u0627\u0631\u0634\u0647\u200c\u0645_\u067e\u06ce\u0646\u062c\u0634\u0647\u200c\u0645_\u0647\u0647\u200c\u06cc\u0646\u06cc_\u0634\u0647\u200c\u0645\u0645\u0647\u200c".split("_"),weekdaysMin:"\u06cc_\u062f_\u0633_\u0686_\u067e_\u0647_\u0634".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},meridiemParse:/\u0626\u06ce\u0648\u0627\u0631\u0647\u200c|\u0628\u0647\u200c\u06cc\u0627\u0646\u06cc/,isPM:function(e){return/\u0626\u06ce\u0648\u0627\u0631\u0647\u200c/.test(e)},meridiem:function(e,a,t){return e<12?"\u0628\u0647\u200c\u06cc\u0627\u0646\u06cc":"\u0626\u06ce\u0648\u0627\u0631\u0647\u200c"},calendar:{sameDay:"[\u0626\u0647\u200c\u0645\u0631\u06c6 \u0643\u0627\u062a\u0698\u0645\u06ce\u0631] LT",nextDay:"[\u0628\u0647\u200c\u06cc\u0627\u0646\u06cc \u0643\u0627\u062a\u0698\u0645\u06ce\u0631] LT",nextWeek:"dddd [\u0643\u0627\u062a\u0698\u0645\u06ce\u0631] LT",lastDay:"[\u062f\u0648\u06ce\u0646\u06ce \u0643\u0627\u062a\u0698\u0645\u06ce\u0631] LT",lastWeek:"dddd [\u0643\u0627\u062a\u0698\u0645\u06ce\u0631] LT",sameElse:"L"},relativeTime:{future:"\u0644\u0647\u200c %s",past:"%s",s:"\u0686\u0647\u200c\u0646\u062f \u0686\u0631\u0643\u0647\u200c\u06cc\u0647\u200c\u0643",ss:"\u0686\u0631\u0643\u0647\u200c %d",m:"\u06cc\u0647\u200c\u0643 \u062e\u0648\u0644\u0647\u200c\u0643",mm:"%d \u062e\u0648\u0644\u0647\u200c\u0643",h:"\u06cc\u0647\u200c\u0643 \u0643\u0627\u062a\u0698\u0645\u06ce\u0631",hh:"%d \u0643\u0627\u062a\u0698\u0645\u06ce\u0631",d:"\u06cc\u0647\u200c\u0643 \u0695\u06c6\u0698",dd:"%d \u0695\u06c6\u0698",M:"\u06cc\u0647\u200c\u0643 \u0645\u0627\u0646\u06af",MM:"%d \u0645\u0627\u0646\u06af",y:"\u06cc\u0647\u200c\u0643 \u0633\u0627\u06b5",yy:"%d \u0633\u0627\u06b5"},preparse:function(e){return e.replace(/[\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669\u0660]/g,function(e){return Rs[e]}).replace(/\u060c/g,",")},postformat:function(e){return e.replace(/\d/g,function(e){return Js[e]}).replace(/,/g,"\u060c")},week:{dow:6,doy:12}}),{0:"-\u0447\u04af",1:"-\u0447\u0438",2:"-\u0447\u0438",3:"-\u0447\u04af",4:"-\u0447\u04af",5:"-\u0447\u0438",6:"-\u0447\u044b",7:"-\u0447\u0438",8:"-\u0447\u0438",9:"-\u0447\u0443",10:"-\u0447\u0443",20:"-\u0447\u044b",30:"-\u0447\u0443",40:"-\u0447\u044b",50:"-\u0447\u04af",60:"-\u0447\u044b",70:"-\u0447\u0438",80:"-\u0447\u0438",90:"-\u0447\u0443",100:"-\u0447\u04af"});function Is(e,a,t,s){var n={m:["eng Minutt","enger Minutt"],h:["eng Stonn","enger Stonn"],d:["een Dag","engem Dag"],M:["ee Mount","engem Mount"],y:["ee Joer","engem Joer"]};return a?n[t][0]:n[t][1]}function Us(e){if(e=parseInt(e,10),isNaN(e))return!1;if(e<0)return!0;if(e<10)return 4<=e&&e<=7;var a;if(e<100)return Us(0==(a=e%10)?e/10:a);if(e<1e4){for(;10<=e;)e/=10;return Us(e)}return Us(e/=1e3)}M.defineLocale("ky",{months:"\u044f\u043d\u0432\u0430\u0440\u044c_\u0444\u0435\u0432\u0440\u0430\u043b\u044c_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0435\u043b\u044c_\u043c\u0430\u0439_\u0438\u044e\u043d\u044c_\u0438\u044e\u043b\u044c_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043d\u0442\u044f\u0431\u0440\u044c_\u043e\u043a\u0442\u044f\u0431\u0440\u044c_\u043d\u043e\u044f\u0431\u0440\u044c_\u0434\u0435\u043a\u0430\u0431\u0440\u044c".split("_"),monthsShort:"\u044f\u043d\u0432_\u0444\u0435\u0432_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440_\u043c\u0430\u0439_\u0438\u044e\u043d\u044c_\u0438\u044e\u043b\u044c_\u0430\u0432\u0433_\u0441\u0435\u043d_\u043e\u043a\u0442_\u043d\u043e\u044f_\u0434\u0435\u043a".split("_"),weekdays:"\u0416\u0435\u043a\u0448\u0435\u043c\u0431\u0438_\u0414\u04af\u0439\u0448\u04e9\u043c\u0431\u04af_\u0428\u0435\u0439\u0448\u0435\u043c\u0431\u0438_\u0428\u0430\u0440\u0448\u0435\u043c\u0431\u0438_\u0411\u0435\u0439\u0448\u0435\u043c\u0431\u0438_\u0416\u0443\u043c\u0430_\u0418\u0448\u0435\u043c\u0431\u0438".split("_"),weekdaysShort:"\u0416\u0435\u043a_\u0414\u04af\u0439_\u0428\u0435\u0439_\u0428\u0430\u0440_\u0411\u0435\u0439_\u0416\u0443\u043c_\u0418\u0448\u0435".split("_"),weekdaysMin:"\u0416\u043a_\u0414\u0439_\u0428\u0439_\u0428\u0440_\u0411\u0439_\u0416\u043c_\u0418\u0448".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0411\u04af\u0433\u04af\u043d \u0441\u0430\u0430\u0442] LT",nextDay:"[\u042d\u0440\u0442\u0435\u04a3 \u0441\u0430\u0430\u0442] LT",nextWeek:"dddd [\u0441\u0430\u0430\u0442] LT",lastDay:"[\u041a\u0435\u0447\u044d\u044d \u0441\u0430\u0430\u0442] LT",lastWeek:"[\u04e8\u0442\u043a\u04e9\u043d \u0430\u043f\u0442\u0430\u043d\u044b\u043d] dddd [\u043a\u04af\u043d\u04af] [\u0441\u0430\u0430\u0442] LT",sameElse:"L"},relativeTime:{future:"%s \u0438\u0447\u0438\u043d\u0434\u0435",past:"%s \u043c\u0443\u0440\u0443\u043d",s:"\u0431\u0438\u0440\u043d\u0435\u0447\u0435 \u0441\u0435\u043a\u0443\u043d\u0434",ss:"%d \u0441\u0435\u043a\u0443\u043d\u0434",m:"\u0431\u0438\u0440 \u043c\u04af\u043d\u04e9\u0442",mm:"%d \u043c\u04af\u043d\u04e9\u0442",h:"\u0431\u0438\u0440 \u0441\u0430\u0430\u0442",hh:"%d \u0441\u0430\u0430\u0442",d:"\u0431\u0438\u0440 \u043a\u04af\u043d",dd:"%d \u043a\u04af\u043d",M:"\u0431\u0438\u0440 \u0430\u0439",MM:"%d \u0430\u0439",y:"\u0431\u0438\u0440 \u0436\u044b\u043b",yy:"%d \u0436\u044b\u043b"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0447\u0438|\u0447\u044b|\u0447\u04af|\u0447\u0443)/,ordinal:function(e){return e+(Cs[e]||Cs[e%10]||Cs[100<=e?100:null])},week:{dow:1,doy:7}}),M.defineLocale("lb",{months:"Januar_Februar_M\xe4erz_Abr\xebll_Mee_Juni_Juli_August_September_Oktober_November_Dezember".split("_"),monthsShort:"Jan._Febr._Mrz._Abr._Mee_Jun._Jul._Aug._Sept._Okt._Nov._Dez.".split("_"),monthsParseExact:!0,weekdays:"Sonndeg_M\xe9indeg_D\xebnschdeg_M\xebttwoch_Donneschdeg_Freideg_Samschdeg".split("_"),weekdaysShort:"So._M\xe9._D\xeb._M\xeb._Do._Fr._Sa.".split("_"),weekdaysMin:"So_M\xe9_D\xeb_M\xeb_Do_Fr_Sa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm [Auer]",LTS:"H:mm:ss [Auer]",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm [Auer]",LLLL:"dddd, D. MMMM YYYY H:mm [Auer]"},calendar:{sameDay:"[Haut um] LT",sameElse:"L",nextDay:"[Muer um] LT",nextWeek:"dddd [um] LT",lastDay:"[G\xebschter um] LT",lastWeek:function(){switch(this.day()){case 2:case 4:return"[Leschten] dddd [um] LT";default:return"[Leschte] dddd [um] LT"}}},relativeTime:{future:function(e){return Us(e.substr(0,e.indexOf(" ")))?"a "+e:"an "+e},past:function(e){return Us(e.substr(0,e.indexOf(" ")))?"viru "+e:"virun "+e},s:"e puer Sekonnen",ss:"%d Sekonnen",m:Is,mm:"%d Minutten",h:Is,hh:"%d Stonnen",d:Is,dd:"%d Deeg",M:Is,MM:"%d M\xe9int",y:Is,yy:"%d Joer"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("lo",{months:"\u0ea1\u0eb1\u0e87\u0e81\u0ead\u0e99_\u0e81\u0eb8\u0ea1\u0e9e\u0eb2_\u0ea1\u0eb5\u0e99\u0eb2_\u0ec0\u0ea1\u0eaa\u0eb2_\u0e9e\u0eb6\u0e94\u0eaa\u0eb0\u0e9e\u0eb2_\u0ea1\u0eb4\u0e96\u0eb8\u0e99\u0eb2_\u0e81\u0ecd\u0ea5\u0eb0\u0e81\u0ebb\u0e94_\u0eaa\u0eb4\u0e87\u0eab\u0eb2_\u0e81\u0eb1\u0e99\u0e8d\u0eb2_\u0e95\u0eb8\u0ea5\u0eb2_\u0e9e\u0eb0\u0e88\u0eb4\u0e81_\u0e97\u0eb1\u0e99\u0ea7\u0eb2".split("_"),monthsShort:"\u0ea1\u0eb1\u0e87\u0e81\u0ead\u0e99_\u0e81\u0eb8\u0ea1\u0e9e\u0eb2_\u0ea1\u0eb5\u0e99\u0eb2_\u0ec0\u0ea1\u0eaa\u0eb2_\u0e9e\u0eb6\u0e94\u0eaa\u0eb0\u0e9e\u0eb2_\u0ea1\u0eb4\u0e96\u0eb8\u0e99\u0eb2_\u0e81\u0ecd\u0ea5\u0eb0\u0e81\u0ebb\u0e94_\u0eaa\u0eb4\u0e87\u0eab\u0eb2_\u0e81\u0eb1\u0e99\u0e8d\u0eb2_\u0e95\u0eb8\u0ea5\u0eb2_\u0e9e\u0eb0\u0e88\u0eb4\u0e81_\u0e97\u0eb1\u0e99\u0ea7\u0eb2".split("_"),weekdays:"\u0ead\u0eb2\u0e97\u0eb4\u0e94_\u0e88\u0eb1\u0e99_\u0ead\u0eb1\u0e87\u0e84\u0eb2\u0e99_\u0e9e\u0eb8\u0e94_\u0e9e\u0eb0\u0eab\u0eb1\u0e94_\u0eaa\u0eb8\u0e81_\u0ec0\u0eaa\u0ebb\u0eb2".split("_"),weekdaysShort:"\u0e97\u0eb4\u0e94_\u0e88\u0eb1\u0e99_\u0ead\u0eb1\u0e87\u0e84\u0eb2\u0e99_\u0e9e\u0eb8\u0e94_\u0e9e\u0eb0\u0eab\u0eb1\u0e94_\u0eaa\u0eb8\u0e81_\u0ec0\u0eaa\u0ebb\u0eb2".split("_"),weekdaysMin:"\u0e97_\u0e88_\u0ead\u0e84_\u0e9e_\u0e9e\u0eab_\u0eaa\u0e81_\u0eaa".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"\u0ea7\u0eb1\u0e99dddd D MMMM YYYY HH:mm"},meridiemParse:/\u0e95\u0ead\u0e99\u0ec0\u0e8a\u0ebb\u0ec9\u0eb2|\u0e95\u0ead\u0e99\u0ec1\u0ea5\u0e87/,isPM:function(e){return"\u0e95\u0ead\u0e99\u0ec1\u0ea5\u0e87"===e},meridiem:function(e,a,t){return e<12?"\u0e95\u0ead\u0e99\u0ec0\u0e8a\u0ebb\u0ec9\u0eb2":"\u0e95\u0ead\u0e99\u0ec1\u0ea5\u0e87"},calendar:{sameDay:"[\u0ea1\u0eb7\u0ec9\u0e99\u0eb5\u0ec9\u0ec0\u0ea7\u0ea5\u0eb2] LT",nextDay:"[\u0ea1\u0eb7\u0ec9\u0ead\u0eb7\u0ec8\u0e99\u0ec0\u0ea7\u0ea5\u0eb2] LT",nextWeek:"[\u0ea7\u0eb1\u0e99]dddd[\u0edc\u0ec9\u0eb2\u0ec0\u0ea7\u0ea5\u0eb2] LT",lastDay:"[\u0ea1\u0eb7\u0ec9\u0ea7\u0eb2\u0e99\u0e99\u0eb5\u0ec9\u0ec0\u0ea7\u0ea5\u0eb2] LT",lastWeek:"[\u0ea7\u0eb1\u0e99]dddd[\u0ec1\u0ea5\u0ec9\u0ea7\u0e99\u0eb5\u0ec9\u0ec0\u0ea7\u0ea5\u0eb2] LT",sameElse:"L"},relativeTime:{future:"\u0ead\u0eb5\u0e81 %s",past:"%s\u0e9c\u0ec8\u0eb2\u0e99\u0ea1\u0eb2",s:"\u0e9a\u0ecd\u0ec8\u0ec0\u0e97\u0ebb\u0ec8\u0eb2\u0ec3\u0e94\u0ea7\u0eb4\u0e99\u0eb2\u0e97\u0eb5",ss:"%d \u0ea7\u0eb4\u0e99\u0eb2\u0e97\u0eb5",m:"1 \u0e99\u0eb2\u0e97\u0eb5",mm:"%d \u0e99\u0eb2\u0e97\u0eb5",h:"1 \u0e8a\u0ebb\u0ec8\u0ea7\u0ec2\u0ea1\u0e87",hh:"%d \u0e8a\u0ebb\u0ec8\u0ea7\u0ec2\u0ea1\u0e87",d:"1 \u0ea1\u0eb7\u0ec9",dd:"%d \u0ea1\u0eb7\u0ec9",M:"1 \u0ec0\u0e94\u0eb7\u0ead\u0e99",MM:"%d \u0ec0\u0e94\u0eb7\u0ead\u0e99",y:"1 \u0e9b\u0eb5",yy:"%d \u0e9b\u0eb5"},dayOfMonthOrdinalParse:/(\u0e97\u0eb5\u0ec8)\d{1,2}/,ordinal:function(e){return"\u0e97\u0eb5\u0ec8"+e}});var Gs={ss:"sekund\u0117_sekund\u017ei\u0173_sekundes",m:"minut\u0117_minut\u0117s_minut\u0119",mm:"minut\u0117s_minu\u010di\u0173_minutes",h:"valanda_valandos_valand\u0105",hh:"valandos_valand\u0173_valandas",d:"diena_dienos_dien\u0105",dd:"dienos_dien\u0173_dienas",M:"m\u0117nuo_m\u0117nesio_m\u0117nes\u012f",MM:"m\u0117nesiai_m\u0117nesi\u0173_m\u0117nesius",y:"metai_met\u0173_metus",yy:"metai_met\u0173_metus"};function Vs(e,a,t,s){return a?Ks(t)[0]:s?Ks(t)[1]:Ks(t)[2]}function Bs(e){return e%10==0||10<e&&e<20}function Ks(e){return Gs[e].split("_")}function qs(e,a,t,s){var n=e+" ";return 1===e?n+Vs(0,a,t[0],s):a?n+(Bs(e)?Ks(t)[1]:Ks(t)[0]):s?n+Ks(t)[1]:n+(Bs(e)?Ks(t)[1]:Ks(t)[2])}M.defineLocale("lt",{months:{format:"sausio_vasario_kovo_baland\u017eio_gegu\u017e\u0117s_bir\u017eelio_liepos_rugpj\u016b\u010dio_rugs\u0117jo_spalio_lapkri\u010dio_gruod\u017eio".split("_"),standalone:"sausis_vasaris_kovas_balandis_gegu\u017e\u0117_bir\u017eelis_liepa_rugpj\u016btis_rugs\u0117jis_spalis_lapkritis_gruodis".split("_"),isFormat:/D[oD]?(\[[^\[\]]*\]|\s)+MMMM?|MMMM?(\[[^\[\]]*\]|\s)+D[oD]?/},monthsShort:"sau_vas_kov_bal_geg_bir_lie_rgp_rgs_spa_lap_grd".split("_"),weekdays:{format:"sekmadien\u012f_pirmadien\u012f_antradien\u012f_tre\u010diadien\u012f_ketvirtadien\u012f_penktadien\u012f_\u0161e\u0161tadien\u012f".split("_"),standalone:"sekmadienis_pirmadienis_antradienis_tre\u010diadienis_ketvirtadienis_penktadienis_\u0161e\u0161tadienis".split("_"),isFormat:/dddd HH:mm/},weekdaysShort:"Sek_Pir_Ant_Tre_Ket_Pen_\u0160e\u0161".split("_"),weekdaysMin:"S_P_A_T_K_Pn_\u0160".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"YYYY [m.] MMMM D [d.]",LLL:"YYYY [m.] MMMM D [d.], HH:mm [val.]",LLLL:"YYYY [m.] MMMM D [d.], dddd, HH:mm [val.]",l:"YYYY-MM-DD",ll:"YYYY [m.] MMMM D [d.]",lll:"YYYY [m.] MMMM D [d.], HH:mm [val.]",llll:"YYYY [m.] MMMM D [d.], ddd, HH:mm [val.]"},calendar:{sameDay:"[\u0160iandien] LT",nextDay:"[Rytoj] LT",nextWeek:"dddd LT",lastDay:"[Vakar] LT",lastWeek:"[Pra\u0117jus\u012f] dddd LT",sameElse:"L"},relativeTime:{future:"po %s",past:"prie\u0161 %s",s:function(e,a,t,s){return a?"kelios sekund\u0117s":s?"keli\u0173 sekund\u017ei\u0173":"kelias sekundes"},ss:qs,m:Vs,mm:qs,h:Vs,hh:qs,d:Vs,dd:qs,M:Vs,MM:qs,y:Vs,yy:qs},dayOfMonthOrdinalParse:/\d{1,2}-oji/,ordinal:function(e){return e+"-oji"},week:{dow:1,doy:4}});var Zs={ss:"sekundes_sekund\u0113m_sekunde_sekundes".split("_"),m:"min\u016btes_min\u016bt\u0113m_min\u016bte_min\u016btes".split("_"),mm:"min\u016btes_min\u016bt\u0113m_min\u016bte_min\u016btes".split("_"),h:"stundas_stund\u0101m_stunda_stundas".split("_"),hh:"stundas_stund\u0101m_stunda_stundas".split("_"),d:"dienas_dien\u0101m_diena_dienas".split("_"),dd:"dienas_dien\u0101m_diena_dienas".split("_"),M:"m\u0113ne\u0161a_m\u0113ne\u0161iem_m\u0113nesis_m\u0113ne\u0161i".split("_"),MM:"m\u0113ne\u0161a_m\u0113ne\u0161iem_m\u0113nesis_m\u0113ne\u0161i".split("_"),y:"gada_gadiem_gads_gadi".split("_"),yy:"gada_gadiem_gads_gadi".split("_")};function $s(e,a,t){return t?a%10==1&&a%100!=11?e[2]:e[3]:a%10==1&&a%100!=11?e[0]:e[1]}function Qs(e,a,t){return e+" "+$s(Zs[t],e,a)}function Xs(e,a,t){return $s(Zs[t],e,a)}M.defineLocale("lv",{months:"janv\u0101ris_febru\u0101ris_marts_apr\u012blis_maijs_j\u016bnijs_j\u016blijs_augusts_septembris_oktobris_novembris_decembris".split("_"),monthsShort:"jan_feb_mar_apr_mai_j\u016bn_j\u016bl_aug_sep_okt_nov_dec".split("_"),weekdays:"sv\u0113tdiena_pirmdiena_otrdiena_tre\u0161diena_ceturtdiena_piektdiena_sestdiena".split("_"),weekdaysShort:"Sv_P_O_T_C_Pk_S".split("_"),weekdaysMin:"Sv_P_O_T_C_Pk_S".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY.",LL:"YYYY. [gada] D. MMMM",LLL:"YYYY. [gada] D. MMMM, HH:mm",LLLL:"YYYY. [gada] D. MMMM, dddd, HH:mm"},calendar:{sameDay:"[\u0160odien pulksten] LT",nextDay:"[R\u012bt pulksten] LT",nextWeek:"dddd [pulksten] LT",lastDay:"[Vakar pulksten] LT",lastWeek:"[Pag\u0101ju\u0161\u0101] dddd [pulksten] LT",sameElse:"L"},relativeTime:{future:"p\u0113c %s",past:"pirms %s",s:function(e,a){return a?"da\u017eas sekundes":"da\u017e\u0101m sekund\u0113m"},ss:Qs,m:Xs,mm:Qs,h:Xs,hh:Qs,d:Xs,dd:Qs,M:Xs,MM:Qs,y:Xs,yy:Qs},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}});var en={words:{ss:["sekund","sekunda","sekundi"],m:["jedan minut","jednog minuta"],mm:["minut","minuta","minuta"],h:["jedan sat","jednog sata"],hh:["sat","sata","sati"],dd:["dan","dana","dana"],MM:["mjesec","mjeseca","mjeseci"],yy:["godina","godine","godina"]},correctGrammaticalCase:function(e,a){return 1===e?a[0]:2<=e&&e<=4?a[1]:a[2]},translate:function(e,a,t){var s=en.words[t];return 1===t.length?a?s[0]:s[1]:e+" "+en.correctGrammaticalCase(e,s)}};function an(e,a,t,s){switch(t){case"s":return a?"\u0445\u044d\u0434\u0445\u044d\u043d \u0441\u0435\u043a\u0443\u043d\u0434":"\u0445\u044d\u0434\u0445\u044d\u043d \u0441\u0435\u043a\u0443\u043d\u0434\u044b\u043d";case"ss":return e+(a?" \u0441\u0435\u043a\u0443\u043d\u0434":" \u0441\u0435\u043a\u0443\u043d\u0434\u044b\u043d");case"m":case"mm":return e+(a?" \u043c\u0438\u043d\u0443\u0442":" \u043c\u0438\u043d\u0443\u0442\u044b\u043d");case"h":case"hh":return e+(a?" \u0446\u0430\u0433":" \u0446\u0430\u0433\u0438\u0439\u043d");case"d":case"dd":return e+(a?" \u04e9\u0434\u04e9\u0440":" \u04e9\u0434\u0440\u0438\u0439\u043d");case"M":case"MM":return e+(a?" \u0441\u0430\u0440":" \u0441\u0430\u0440\u044b\u043d");case"y":case"yy":return e+(a?" \u0436\u0438\u043b":" \u0436\u0438\u043b\u0438\u0439\u043d");default:return e}}M.defineLocale("me",{months:"januar_februar_mart_april_maj_jun_jul_avgust_septembar_oktobar_novembar_decembar".split("_"),monthsShort:"jan._feb._mar._apr._maj_jun_jul_avg._sep._okt._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"nedjelja_ponedjeljak_utorak_srijeda_\u010detvrtak_petak_subota".split("_"),weekdaysShort:"ned._pon._uto._sri._\u010det._pet._sub.".split("_"),weekdaysMin:"ne_po_ut_sr_\u010de_pe_su".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd, D. MMMM YYYY H:mm"},calendar:{sameDay:"[danas u] LT",nextDay:"[sjutra u] LT",nextWeek:function(){switch(this.day()){case 0:return"[u] [nedjelju] [u] LT";case 3:return"[u] [srijedu] [u] LT";case 6:return"[u] [subotu] [u] LT";case 1:case 2:case 4:case 5:return"[u] dddd [u] LT"}},lastDay:"[ju\u010de u] LT",lastWeek:function(){return["[pro\u0161le] [nedjelje] [u] LT","[pro\u0161log] [ponedjeljka] [u] LT","[pro\u0161log] [utorka] [u] LT","[pro\u0161le] [srijede] [u] LT","[pro\u0161log] [\u010detvrtka] [u] LT","[pro\u0161log] [petka] [u] LT","[pro\u0161le] [subote] [u] LT"][this.day()]},sameElse:"L"},relativeTime:{future:"za %s",past:"prije %s",s:"nekoliko sekundi",ss:en.translate,m:en.translate,mm:en.translate,h:en.translate,hh:en.translate,d:"dan",dd:en.translate,M:"mjesec",MM:en.translate,y:"godinu",yy:en.translate},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}}),M.defineLocale("mi",{months:"Kohi-t\u0101te_Hui-tanguru_Pout\u016b-te-rangi_Paenga-wh\u0101wh\u0101_Haratua_Pipiri_H\u014dngoingoi_Here-turi-k\u014dk\u0101_Mahuru_Whiringa-\u0101-nuku_Whiringa-\u0101-rangi_Hakihea".split("_"),monthsShort:"Kohi_Hui_Pou_Pae_Hara_Pipi_H\u014dngoi_Here_Mahu_Whi-nu_Whi-ra_Haki".split("_"),monthsRegex:/(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i,monthsStrictRegex:/(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i,monthsShortRegex:/(?:['a-z\u0101\u014D\u016B]+\-?){1,3}/i,monthsShortStrictRegex:/(?:['a-z\u0101\u014D\u016B]+\-?){1,2}/i,weekdays:"R\u0101tapu_Mane_T\u016brei_Wenerei_T\u0101ite_Paraire_H\u0101tarei".split("_"),weekdaysShort:"Ta_Ma_T\u016b_We_T\u0101i_Pa_H\u0101".split("_"),weekdaysMin:"Ta_Ma_T\u016b_We_T\u0101i_Pa_H\u0101".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [i] HH:mm",LLLL:"dddd, D MMMM YYYY [i] HH:mm"},calendar:{sameDay:"[i teie mahana, i] LT",nextDay:"[apopo i] LT",nextWeek:"dddd [i] LT",lastDay:"[inanahi i] LT",lastWeek:"dddd [whakamutunga i] LT",sameElse:"L"},relativeTime:{future:"i roto i %s",past:"%s i mua",s:"te h\u0113kona ruarua",ss:"%d h\u0113kona",m:"he meneti",mm:"%d meneti",h:"te haora",hh:"%d haora",d:"he ra",dd:"%d ra",M:"he marama",MM:"%d marama",y:"he tau",yy:"%d tau"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),M.defineLocale("mk",{months:"\u0458\u0430\u043d\u0443\u0430\u0440\u0438_\u0444\u0435\u0432\u0440\u0443\u0430\u0440\u0438_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0438\u043b_\u043c\u0430\u0458_\u0458\u0443\u043d\u0438_\u0458\u0443\u043b\u0438_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043f\u0442\u0435\u043c\u0432\u0440\u0438_\u043e\u043a\u0442\u043e\u043c\u0432\u0440\u0438_\u043d\u043e\u0435\u043c\u0432\u0440\u0438_\u0434\u0435\u043a\u0435\u043c\u0432\u0440\u0438".split("_"),monthsShort:"\u0458\u0430\u043d_\u0444\u0435\u0432_\u043c\u0430\u0440_\u0430\u043f\u0440_\u043c\u0430\u0458_\u0458\u0443\u043d_\u0458\u0443\u043b_\u0430\u0432\u0433_\u0441\u0435\u043f_\u043e\u043a\u0442_\u043d\u043e\u0435_\u0434\u0435\u043a".split("_"),weekdays:"\u043d\u0435\u0434\u0435\u043b\u0430_\u043f\u043e\u043d\u0435\u0434\u0435\u043b\u043d\u0438\u043a_\u0432\u0442\u043e\u0440\u043d\u0438\u043a_\u0441\u0440\u0435\u0434\u0430_\u0447\u0435\u0442\u0432\u0440\u0442\u043e\u043a_\u043f\u0435\u0442\u043e\u043a_\u0441\u0430\u0431\u043e\u0442\u0430".split("_"),weekdaysShort:"\u043d\u0435\u0434_\u043f\u043e\u043d_\u0432\u0442\u043e_\u0441\u0440\u0435_\u0447\u0435\u0442_\u043f\u0435\u0442_\u0441\u0430\u0431".split("_"),weekdaysMin:"\u043de_\u043fo_\u0432\u0442_\u0441\u0440_\u0447\u0435_\u043f\u0435_\u0441a".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"D.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY H:mm",LLLL:"dddd, D MMMM YYYY H:mm"},calendar:{sameDay:"[\u0414\u0435\u043d\u0435\u0441 \u0432\u043e] LT",nextDay:"[\u0423\u0442\u0440\u0435 \u0432\u043e] LT",nextWeek:"[\u0412\u043e] dddd [\u0432\u043e] LT",lastDay:"[\u0412\u0447\u0435\u0440\u0430 \u0432\u043e] LT",lastWeek:function(){switch(this.day()){case 0:case 3:case 6:return"[\u0418\u0437\u043c\u0438\u043d\u0430\u0442\u0430\u0442\u0430] dddd [\u0432\u043e] LT";case 1:case 2:case 4:case 5:return"[\u0418\u0437\u043c\u0438\u043d\u0430\u0442\u0438\u043e\u0442] dddd [\u0432\u043e] LT"}},sameElse:"L"},relativeTime:{future:"\u0437\u0430 %s",past:"\u043f\u0440\u0435\u0434 %s",s:"\u043d\u0435\u043a\u043e\u043b\u043a\u0443 \u0441\u0435\u043a\u0443\u043d\u0434\u0438",ss:"%d \u0441\u0435\u043a\u0443\u043d\u0434\u0438",m:"\u0435\u0434\u043d\u0430 \u043c\u0438\u043d\u0443\u0442\u0430",mm:"%d \u043c\u0438\u043d\u0443\u0442\u0438",h:"\u0435\u0434\u0435\u043d \u0447\u0430\u0441",hh:"%d \u0447\u0430\u0441\u0430",d:"\u0435\u0434\u0435\u043d \u0434\u0435\u043d",dd:"%d \u0434\u0435\u043d\u0430",M:"\u0435\u0434\u0435\u043d \u043c\u0435\u0441\u0435\u0446",MM:"%d \u043c\u0435\u0441\u0435\u0446\u0438",y:"\u0435\u0434\u043d\u0430 \u0433\u043e\u0434\u0438\u043d\u0430",yy:"%d \u0433\u043e\u0434\u0438\u043d\u0438"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0435\u0432|\u0435\u043d|\u0442\u0438|\u0432\u0438|\u0440\u0438|\u043c\u0438)/,ordinal:function(e){var a=e%10,t=e%100;return 0===e?e+"-\u0435\u0432":0==t?e+"-\u0435\u043d":10<t&&t<20?e+"-\u0442\u0438":1==a?e+"-\u0432\u0438":2==a?e+"-\u0440\u0438":7==a||8==a?e+"-\u043c\u0438":e+"-\u0442\u0438"},week:{dow:1,doy:7}}),M.defineLocale("ml",{months:"\u0d1c\u0d28\u0d41\u0d35\u0d30\u0d3f_\u0d2b\u0d46\u0d2c\u0d4d\u0d30\u0d41\u0d35\u0d30\u0d3f_\u0d2e\u0d3e\u0d7c\u0d1a\u0d4d\u0d1a\u0d4d_\u0d0f\u0d2a\u0d4d\u0d30\u0d3f\u0d7d_\u0d2e\u0d47\u0d2f\u0d4d_\u0d1c\u0d42\u0d7a_\u0d1c\u0d42\u0d32\u0d48_\u0d13\u0d17\u0d38\u0d4d\u0d31\u0d4d\u0d31\u0d4d_\u0d38\u0d46\u0d2a\u0d4d\u0d31\u0d4d\u0d31\u0d02\u0d2c\u0d7c_\u0d12\u0d15\u0d4d\u0d1f\u0d4b\u0d2c\u0d7c_\u0d28\u0d35\u0d02\u0d2c\u0d7c_\u0d21\u0d3f\u0d38\u0d02\u0d2c\u0d7c".split("_"),monthsShort:"\u0d1c\u0d28\u0d41._\u0d2b\u0d46\u0d2c\u0d4d\u0d30\u0d41._\u0d2e\u0d3e\u0d7c._\u0d0f\u0d2a\u0d4d\u0d30\u0d3f._\u0d2e\u0d47\u0d2f\u0d4d_\u0d1c\u0d42\u0d7a_\u0d1c\u0d42\u0d32\u0d48._\u0d13\u0d17._\u0d38\u0d46\u0d2a\u0d4d\u0d31\u0d4d\u0d31._\u0d12\u0d15\u0d4d\u0d1f\u0d4b._\u0d28\u0d35\u0d02._\u0d21\u0d3f\u0d38\u0d02.".split("_"),monthsParseExact:!0,weekdays:"\u0d1e\u0d3e\u0d2f\u0d31\u0d3e\u0d34\u0d4d\u0d1a_\u0d24\u0d3f\u0d19\u0d4d\u0d15\u0d33\u0d3e\u0d34\u0d4d\u0d1a_\u0d1a\u0d4a\u0d35\u0d4d\u0d35\u0d3e\u0d34\u0d4d\u0d1a_\u0d2c\u0d41\u0d27\u0d28\u0d3e\u0d34\u0d4d\u0d1a_\u0d35\u0d4d\u0d2f\u0d3e\u0d34\u0d3e\u0d34\u0d4d\u0d1a_\u0d35\u0d46\u0d33\u0d4d\u0d33\u0d3f\u0d2f\u0d3e\u0d34\u0d4d\u0d1a_\u0d36\u0d28\u0d3f\u0d2f\u0d3e\u0d34\u0d4d\u0d1a".split("_"),weekdaysShort:"\u0d1e\u0d3e\u0d2f\u0d7c_\u0d24\u0d3f\u0d19\u0d4d\u0d15\u0d7e_\u0d1a\u0d4a\u0d35\u0d4d\u0d35_\u0d2c\u0d41\u0d27\u0d7b_\u0d35\u0d4d\u0d2f\u0d3e\u0d34\u0d02_\u0d35\u0d46\u0d33\u0d4d\u0d33\u0d3f_\u0d36\u0d28\u0d3f".split("_"),weekdaysMin:"\u0d1e\u0d3e_\u0d24\u0d3f_\u0d1a\u0d4a_\u0d2c\u0d41_\u0d35\u0d4d\u0d2f\u0d3e_\u0d35\u0d46_\u0d36".split("_"),longDateFormat:{LT:"A h:mm -\u0d28\u0d41",LTS:"A h:mm:ss -\u0d28\u0d41",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm -\u0d28\u0d41",LLLL:"dddd, D MMMM YYYY, A h:mm -\u0d28\u0d41"},calendar:{sameDay:"[\u0d07\u0d28\u0d4d\u0d28\u0d4d] LT",nextDay:"[\u0d28\u0d3e\u0d33\u0d46] LT",nextWeek:"dddd, LT",lastDay:"[\u0d07\u0d28\u0d4d\u0d28\u0d32\u0d46] LT",lastWeek:"[\u0d15\u0d34\u0d3f\u0d1e\u0d4d\u0d1e] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0d15\u0d34\u0d3f\u0d1e\u0d4d\u0d1e\u0d4d",past:"%s \u0d2e\u0d41\u0d7b\u0d2a\u0d4d",s:"\u0d05\u0d7d\u0d2a \u0d28\u0d3f\u0d2e\u0d3f\u0d37\u0d19\u0d4d\u0d19\u0d7e",ss:"%d \u0d38\u0d46\u0d15\u0d4d\u0d15\u0d7b\u0d21\u0d4d",m:"\u0d12\u0d30\u0d41 \u0d2e\u0d3f\u0d28\u0d3f\u0d31\u0d4d\u0d31\u0d4d",mm:"%d \u0d2e\u0d3f\u0d28\u0d3f\u0d31\u0d4d\u0d31\u0d4d",h:"\u0d12\u0d30\u0d41 \u0d2e\u0d23\u0d3f\u0d15\u0d4d\u0d15\u0d42\u0d7c",hh:"%d \u0d2e\u0d23\u0d3f\u0d15\u0d4d\u0d15\u0d42\u0d7c",d:"\u0d12\u0d30\u0d41 \u0d26\u0d3f\u0d35\u0d38\u0d02",dd:"%d \u0d26\u0d3f\u0d35\u0d38\u0d02",M:"\u0d12\u0d30\u0d41 \u0d2e\u0d3e\u0d38\u0d02",MM:"%d \u0d2e\u0d3e\u0d38\u0d02",y:"\u0d12\u0d30\u0d41 \u0d35\u0d7c\u0d37\u0d02",yy:"%d \u0d35\u0d7c\u0d37\u0d02"},meridiemParse:/\u0d30\u0d3e\u0d24\u0d4d\u0d30\u0d3f|\u0d30\u0d3e\u0d35\u0d3f\u0d32\u0d46|\u0d09\u0d1a\u0d4d\u0d1a \u0d15\u0d34\u0d3f\u0d1e\u0d4d\u0d1e\u0d4d|\u0d35\u0d48\u0d15\u0d41\u0d28\u0d4d\u0d28\u0d47\u0d30\u0d02|\u0d30\u0d3e\u0d24\u0d4d\u0d30\u0d3f/i,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0d30\u0d3e\u0d24\u0d4d\u0d30\u0d3f"===a&&4<=e||"\u0d09\u0d1a\u0d4d\u0d1a \u0d15\u0d34\u0d3f\u0d1e\u0d4d\u0d1e\u0d4d"===a||"\u0d35\u0d48\u0d15\u0d41\u0d28\u0d4d\u0d28\u0d47\u0d30\u0d02"===a?e+12:e},meridiem:function(e,a,t){return e<4?"\u0d30\u0d3e\u0d24\u0d4d\u0d30\u0d3f":e<12?"\u0d30\u0d3e\u0d35\u0d3f\u0d32\u0d46":e<17?"\u0d09\u0d1a\u0d4d\u0d1a \u0d15\u0d34\u0d3f\u0d1e\u0d4d\u0d1e\u0d4d":e<20?"\u0d35\u0d48\u0d15\u0d41\u0d28\u0d4d\u0d28\u0d47\u0d30\u0d02":"\u0d30\u0d3e\u0d24\u0d4d\u0d30\u0d3f"}}),M.defineLocale("mn",{months:"\u041d\u044d\u0433\u0434\u04af\u0433\u044d\u044d\u0440 \u0441\u0430\u0440_\u0425\u043e\u0451\u0440\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0413\u0443\u0440\u0430\u0432\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0414\u04e9\u0440\u04e9\u0432\u0434\u04af\u0433\u044d\u044d\u0440 \u0441\u0430\u0440_\u0422\u0430\u0432\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0417\u0443\u0440\u0433\u0430\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0414\u043e\u043b\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u041d\u0430\u0439\u043c\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0415\u0441\u0434\u04af\u0433\u044d\u044d\u0440 \u0441\u0430\u0440_\u0410\u0440\u0430\u0432\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440_\u0410\u0440\u0432\u0430\u043d \u043d\u044d\u0433\u0434\u04af\u0433\u044d\u044d\u0440 \u0441\u0430\u0440_\u0410\u0440\u0432\u0430\u043d \u0445\u043e\u0451\u0440\u0434\u0443\u0433\u0430\u0430\u0440 \u0441\u0430\u0440".split("_"),monthsShort:"1 \u0441\u0430\u0440_2 \u0441\u0430\u0440_3 \u0441\u0430\u0440_4 \u0441\u0430\u0440_5 \u0441\u0430\u0440_6 \u0441\u0430\u0440_7 \u0441\u0430\u0440_8 \u0441\u0430\u0440_9 \u0441\u0430\u0440_10 \u0441\u0430\u0440_11 \u0441\u0430\u0440_12 \u0441\u0430\u0440".split("_"),monthsParseExact:!0,weekdays:"\u041d\u044f\u043c_\u0414\u0430\u0432\u0430\u0430_\u041c\u044f\u0433\u043c\u0430\u0440_\u041b\u0445\u0430\u0433\u0432\u0430_\u041f\u04af\u0440\u044d\u0432_\u0411\u0430\u0430\u0441\u0430\u043d_\u0411\u044f\u043c\u0431\u0430".split("_"),weekdaysShort:"\u041d\u044f\u043c_\u0414\u0430\u0432_\u041c\u044f\u0433_\u041b\u0445\u0430_\u041f\u04af\u0440_\u0411\u0430\u0430_\u0411\u044f\u043c".split("_"),weekdaysMin:"\u041d\u044f_\u0414\u0430_\u041c\u044f_\u041b\u0445_\u041f\u04af_\u0411\u0430_\u0411\u044f".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"YYYY \u043e\u043d\u044b MMMM\u044b\u043d D",LLL:"YYYY \u043e\u043d\u044b MMMM\u044b\u043d D HH:mm",LLLL:"dddd, YYYY \u043e\u043d\u044b MMMM\u044b\u043d D HH:mm"},meridiemParse:/\u04ae\u04e8|\u04ae\u0425/i,isPM:function(e){return"\u04ae\u0425"===e},meridiem:function(e,a,t){return e<12?"\u04ae\u04e8":"\u04ae\u0425"},calendar:{sameDay:"[\u04e8\u043d\u04e9\u04e9\u0434\u04e9\u0440] LT",nextDay:"[\u041c\u0430\u0440\u0433\u0430\u0430\u0448] LT",nextWeek:"[\u0418\u0440\u044d\u0445] dddd LT",lastDay:"[\u04e8\u0447\u0438\u0433\u0434\u04e9\u0440] LT",lastWeek:"[\u04e8\u043d\u0433\u04e9\u0440\u0441\u04e9\u043d] dddd LT",sameElse:"L"},relativeTime:{future:"%s \u0434\u0430\u0440\u0430\u0430",past:"%s \u04e9\u043c\u043d\u04e9",s:an,ss:an,m:an,mm:an,h:an,hh:an,d:an,dd:an,M:an,MM:an,y:an,yy:an},dayOfMonthOrdinalParse:/\d{1,2} \u04e9\u0434\u04e9\u0440/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+" \u04e9\u0434\u04e9\u0440";default:return e}}});var tn={1:"\u0967",2:"\u0968",3:"\u0969",4:"\u096a",5:"\u096b",6:"\u096c",7:"\u096d",8:"\u096e",9:"\u096f",0:"\u0966"},sn={"\u0967":"1","\u0968":"2","\u0969":"3","\u096a":"4","\u096b":"5","\u096c":"6","\u096d":"7","\u096e":"8","\u096f":"9","\u0966":"0"};function nn(e,a,t,s){var n="";if(a)switch(t){case"s":n="\u0915\u093e\u0939\u0940 \u0938\u0947\u0915\u0902\u0926";break;case"ss":n="%d \u0938\u0947\u0915\u0902\u0926";break;case"m":n="\u090f\u0915 \u092e\u093f\u0928\u093f\u091f";break;case"mm":n="%d \u092e\u093f\u0928\u093f\u091f\u0947";break;case"h":n="\u090f\u0915 \u0924\u093e\u0938";break;case"hh":n="%d \u0924\u093e\u0938";break;case"d":n="\u090f\u0915 \u0926\u093f\u0935\u0938";break;case"dd":n="%d \u0926\u093f\u0935\u0938";break;case"M":n="\u090f\u0915 \u092e\u0939\u093f\u0928\u093e";break;case"MM":n="%d \u092e\u0939\u093f\u0928\u0947";break;case"y":n="\u090f\u0915 \u0935\u0930\u094d\u0937";break;case"yy":n="%d \u0935\u0930\u094d\u0937\u0947";break}else switch(t){case"s":n="\u0915\u093e\u0939\u0940 \u0938\u0947\u0915\u0902\u0926\u093e\u0902";break;case"ss":n="%d \u0938\u0947\u0915\u0902\u0926\u093e\u0902";break;case"m":n="\u090f\u0915\u093e \u092e\u093f\u0928\u093f\u091f\u093e";break;case"mm":n="%d \u092e\u093f\u0928\u093f\u091f\u093e\u0902";break;case"h":n="\u090f\u0915\u093e \u0924\u093e\u0938\u093e";break;case"hh":n="%d \u0924\u093e\u0938\u093e\u0902";break;case"d":n="\u090f\u0915\u093e \u0926\u093f\u0935\u0938\u093e";break;case"dd":n="%d \u0926\u093f\u0935\u0938\u093e\u0902";break;case"M":n="\u090f\u0915\u093e \u092e\u0939\u093f\u0928\u094d\u092f\u093e";break;case"MM":n="%d \u092e\u0939\u093f\u0928\u094d\u092f\u093e\u0902";break;case"y":n="\u090f\u0915\u093e \u0935\u0930\u094d\u0937\u093e";break;case"yy":n="%d \u0935\u0930\u094d\u0937\u093e\u0902";break}return n.replace(/%d/i,e)}M.defineLocale("mr",{months:"\u091c\u093e\u0928\u0947\u0935\u093e\u0930\u0940_\u092b\u0947\u092c\u094d\u0930\u0941\u0935\u093e\u0930\u0940_\u092e\u093e\u0930\u094d\u091a_\u090f\u092a\u094d\u0930\u093f\u0932_\u092e\u0947_\u091c\u0942\u0928_\u091c\u0941\u0932\u0948_\u0911\u0917\u0938\u094d\u091f_\u0938\u092a\u094d\u091f\u0947\u0902\u092c\u0930_\u0911\u0915\u094d\u091f\u094b\u092c\u0930_\u0928\u094b\u0935\u094d\u0939\u0947\u0902\u092c\u0930_\u0921\u093f\u0938\u0947\u0902\u092c\u0930".split("_"),monthsShort:"\u091c\u093e\u0928\u0947._\u092b\u0947\u092c\u094d\u0930\u0941._\u092e\u093e\u0930\u094d\u091a._\u090f\u092a\u094d\u0930\u093f._\u092e\u0947._\u091c\u0942\u0928._\u091c\u0941\u0932\u0948._\u0911\u0917._\u0938\u092a\u094d\u091f\u0947\u0902._\u0911\u0915\u094d\u091f\u094b._\u0928\u094b\u0935\u094d\u0939\u0947\u0902._\u0921\u093f\u0938\u0947\u0902.".split("_"),monthsParseExact:!0,weekdays:"\u0930\u0935\u093f\u0935\u093e\u0930_\u0938\u094b\u092e\u0935\u093e\u0930_\u092e\u0902\u0917\u0933\u0935\u093e\u0930_\u092c\u0941\u0927\u0935\u093e\u0930_\u0917\u0941\u0930\u0942\u0935\u093e\u0930_\u0936\u0941\u0915\u094d\u0930\u0935\u093e\u0930_\u0936\u0928\u093f\u0935\u093e\u0930".split("_"),weekdaysShort:"\u0930\u0935\u093f_\u0938\u094b\u092e_\u092e\u0902\u0917\u0933_\u092c\u0941\u0927_\u0917\u0941\u0930\u0942_\u0936\u0941\u0915\u094d\u0930_\u0936\u0928\u093f".split("_"),weekdaysMin:"\u0930_\u0938\u094b_\u092e\u0902_\u092c\u0941_\u0917\u0941_\u0936\u0941_\u0936".split("_"),longDateFormat:{LT:"A h:mm \u0935\u093e\u091c\u0924\u093e",LTS:"A h:mm:ss \u0935\u093e\u091c\u0924\u093e",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u0935\u093e\u091c\u0924\u093e",LLLL:"dddd, D MMMM YYYY, A h:mm \u0935\u093e\u091c\u0924\u093e"},calendar:{sameDay:"[\u0906\u091c] LT",nextDay:"[\u0909\u0926\u094d\u092f\u093e] LT",nextWeek:"dddd, LT",lastDay:"[\u0915\u093e\u0932] LT",lastWeek:"[\u092e\u093e\u0917\u0940\u0932] dddd, LT",sameElse:"L"},relativeTime:{future:"%s\u092e\u0927\u094d\u092f\u0947",past:"%s\u092a\u0942\u0930\u094d\u0935\u0940",s:nn,ss:nn,m:nn,mm:nn,h:nn,hh:nn,d:nn,dd:nn,M:nn,MM:nn,y:nn,yy:nn},preparse:function(e){return e.replace(/[\u0967\u0968\u0969\u096a\u096b\u096c\u096d\u096e\u096f\u0966]/g,function(e){return sn[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return tn[e]})},meridiemParse:/\u092a\u0939\u093e\u091f\u0947|\u0938\u0915\u093e\u0933\u0940|\u0926\u0941\u092a\u093e\u0930\u0940|\u0938\u093e\u092f\u0902\u0915\u093e\u0933\u0940|\u0930\u093e\u0924\u094d\u0930\u0940/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u092a\u0939\u093e\u091f\u0947"===a||"\u0938\u0915\u093e\u0933\u0940"===a?e:"\u0926\u0941\u092a\u093e\u0930\u0940"===a||"\u0938\u093e\u092f\u0902\u0915\u093e\u0933\u0940"===a||"\u0930\u093e\u0924\u094d\u0930\u0940"===a?12<=e?e:e+12:void 0},meridiem:function(e,a,t){return 0<=e&&e<6?"\u092a\u0939\u093e\u091f\u0947":e<12?"\u0938\u0915\u093e\u0933\u0940":e<17?"\u0926\u0941\u092a\u093e\u0930\u0940":e<20?"\u0938\u093e\u092f\u0902\u0915\u093e\u0933\u0940":"\u0930\u093e\u0924\u094d\u0930\u0940"},week:{dow:0,doy:6}}),M.defineLocale("ms-my",{months:"Januari_Februari_Mac_April_Mei_Jun_Julai_Ogos_September_Oktober_November_Disember".split("_"),monthsShort:"Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ogs_Sep_Okt_Nov_Dis".split("_"),weekdays:"Ahad_Isnin_Selasa_Rabu_Khamis_Jumaat_Sabtu".split("_"),weekdaysShort:"Ahd_Isn_Sel_Rab_Kha_Jum_Sab".split("_"),weekdaysMin:"Ah_Is_Sl_Rb_Km_Jm_Sb".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [pukul] HH.mm",LLLL:"dddd, D MMMM YYYY [pukul] HH.mm"},meridiemParse:/pagi|tengahari|petang|malam/,meridiemHour:function(e,a){return 12===e&&(e=0),"pagi"===a?e:"tengahari"===a?11<=e?e:e+12:"petang"===a||"malam"===a?e+12:void 0},meridiem:function(e,a,t){return e<11?"pagi":e<15?"tengahari":e<19?"petang":"malam"},calendar:{sameDay:"[Hari ini pukul] LT",nextDay:"[Esok pukul] LT",nextWeek:"dddd [pukul] LT",lastDay:"[Kelmarin pukul] LT",lastWeek:"dddd [lepas pukul] LT",sameElse:"L"},relativeTime:{future:"dalam %s",past:"%s yang lepas",s:"beberapa saat",ss:"%d saat",m:"seminit",mm:"%d minit",h:"sejam",hh:"%d jam",d:"sehari",dd:"%d hari",M:"sebulan",MM:"%d bulan",y:"setahun",yy:"%d tahun"},week:{dow:1,doy:7}}),M.defineLocale("ms",{months:"Januari_Februari_Mac_April_Mei_Jun_Julai_Ogos_September_Oktober_November_Disember".split("_"),monthsShort:"Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ogs_Sep_Okt_Nov_Dis".split("_"),weekdays:"Ahad_Isnin_Selasa_Rabu_Khamis_Jumaat_Sabtu".split("_"),weekdaysShort:"Ahd_Isn_Sel_Rab_Kha_Jum_Sab".split("_"),weekdaysMin:"Ah_Is_Sl_Rb_Km_Jm_Sb".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [pukul] HH.mm",LLLL:"dddd, D MMMM YYYY [pukul] HH.mm"},meridiemParse:/pagi|tengahari|petang|malam/,meridiemHour:function(e,a){return 12===e&&(e=0),"pagi"===a?e:"tengahari"===a?11<=e?e:e+12:"petang"===a||"malam"===a?e+12:void 0},meridiem:function(e,a,t){return e<11?"pagi":e<15?"tengahari":e<19?"petang":"malam"},calendar:{sameDay:"[Hari ini pukul] LT",nextDay:"[Esok pukul] LT",nextWeek:"dddd [pukul] LT",lastDay:"[Kelmarin pukul] LT",lastWeek:"dddd [lepas pukul] LT",sameElse:"L"},relativeTime:{future:"dalam %s",past:"%s yang lepas",s:"beberapa saat",ss:"%d saat",m:"seminit",mm:"%d minit",h:"sejam",hh:"%d jam",d:"sehari",dd:"%d hari",M:"sebulan",MM:"%d bulan",y:"setahun",yy:"%d tahun"},week:{dow:1,doy:7}}),M.defineLocale("mt",{months:"Jannar_Frar_Marzu_April_Mejju_\u0120unju_Lulju_Awwissu_Settembru_Ottubru_Novembru_Di\u010bembru".split("_"),monthsShort:"Jan_Fra_Mar_Apr_Mej_\u0120un_Lul_Aww_Set_Ott_Nov_Di\u010b".split("_"),weekdays:"Il-\u0126add_It-Tnejn_It-Tlieta_L-Erbg\u0127a_Il-\u0126amis_Il-\u0120img\u0127a_Is-Sibt".split("_"),weekdaysShort:"\u0126ad_Tne_Tli_Erb_\u0126am_\u0120im_Sib".split("_"),weekdaysMin:"\u0126a_Tn_Tl_Er_\u0126a_\u0120i_Si".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Illum fil-]LT",nextDay:"[G\u0127ada fil-]LT",nextWeek:"dddd [fil-]LT",lastDay:"[Il-biera\u0127 fil-]LT",lastWeek:"dddd [li g\u0127adda] [fil-]LT",sameElse:"L"},relativeTime:{future:"f\u2019 %s",past:"%s ilu",s:"ftit sekondi",ss:"%d sekondi",m:"minuta",mm:"%d minuti",h:"sieg\u0127a",hh:"%d sieg\u0127at",d:"\u0121urnata",dd:"%d \u0121ranet",M:"xahar",MM:"%d xhur",y:"sena",yy:"%d sni"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}});var rn={1:"\u1041",2:"\u1042",3:"\u1043",4:"\u1044",5:"\u1045",6:"\u1046",7:"\u1047",8:"\u1048",9:"\u1049",0:"\u1040"},dn={"\u1041":"1","\u1042":"2","\u1043":"3","\u1044":"4","\u1045":"5","\u1046":"6","\u1047":"7","\u1048":"8","\u1049":"9","\u1040":"0"},_n=(M.defineLocale("my",{months:"\u1007\u1014\u103a\u1014\u101d\u102b\u101b\u102e_\u1016\u1031\u1016\u1031\u102c\u103a\u101d\u102b\u101b\u102e_\u1019\u1010\u103a_\u1027\u1015\u103c\u102e_\u1019\u1031_\u1007\u103d\u1014\u103a_\u1007\u1030\u101c\u102d\u102f\u1004\u103a_\u101e\u103c\u1002\u102f\u1010\u103a_\u1005\u1000\u103a\u1010\u1004\u103a\u1018\u102c_\u1021\u1031\u102c\u1000\u103a\u1010\u102d\u102f\u1018\u102c_\u1014\u102d\u102f\u101d\u1004\u103a\u1018\u102c_\u1012\u102e\u1007\u1004\u103a\u1018\u102c".split("_"),monthsShort:"\u1007\u1014\u103a_\u1016\u1031_\u1019\u1010\u103a_\u1015\u103c\u102e_\u1019\u1031_\u1007\u103d\u1014\u103a_\u101c\u102d\u102f\u1004\u103a_\u101e\u103c_\u1005\u1000\u103a_\u1021\u1031\u102c\u1000\u103a_\u1014\u102d\u102f_\u1012\u102e".split("_"),weekdays:"\u1010\u1014\u1004\u103a\u1039\u1002\u1014\u103d\u1031_\u1010\u1014\u1004\u103a\u1039\u101c\u102c_\u1021\u1004\u103a\u1039\u1002\u102b_\u1017\u102f\u1012\u1039\u1013\u101f\u1030\u1038_\u1000\u103c\u102c\u101e\u1015\u1010\u1031\u1038_\u101e\u1031\u102c\u1000\u103c\u102c_\u1005\u1014\u1031".split("_"),weekdaysShort:"\u1014\u103d\u1031_\u101c\u102c_\u1002\u102b_\u101f\u1030\u1038_\u1000\u103c\u102c_\u101e\u1031\u102c_\u1014\u1031".split("_"),weekdaysMin:"\u1014\u103d\u1031_\u101c\u102c_\u1002\u102b_\u101f\u1030\u1038_\u1000\u103c\u102c_\u101e\u1031\u102c_\u1014\u1031".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u101a\u1014\u1031.] LT [\u1019\u103e\u102c]",nextDay:"[\u1019\u1014\u1000\u103a\u1016\u103c\u1014\u103a] LT [\u1019\u103e\u102c]",nextWeek:"dddd LT [\u1019\u103e\u102c]",lastDay:"[\u1019\u1014\u1031.\u1000] LT [\u1019\u103e\u102c]",lastWeek:"[\u1015\u103c\u102e\u1038\u1001\u1032\u1037\u101e\u1031\u102c] dddd LT [\u1019\u103e\u102c]",sameElse:"L"},relativeTime:{future:"\u101c\u102c\u1019\u100a\u103a\u1037 %s \u1019\u103e\u102c",past:"\u101c\u103d\u1014\u103a\u1001\u1032\u1037\u101e\u1031\u102c %s \u1000",s:"\u1005\u1000\u1039\u1000\u1014\u103a.\u1021\u1014\u100a\u103a\u1038\u1004\u101a\u103a",ss:"%d \u1005\u1000\u1039\u1000\u1014\u1037\u103a",m:"\u1010\u1005\u103a\u1019\u102d\u1014\u1005\u103a",mm:"%d \u1019\u102d\u1014\u1005\u103a",h:"\u1010\u1005\u103a\u1014\u102c\u101b\u102e",hh:"%d \u1014\u102c\u101b\u102e",d:"\u1010\u1005\u103a\u101b\u1000\u103a",dd:"%d \u101b\u1000\u103a",M:"\u1010\u1005\u103a\u101c",MM:"%d \u101c",y:"\u1010\u1005\u103a\u1014\u103e\u1005\u103a",yy:"%d \u1014\u103e\u1005\u103a"},preparse:function(e){return e.replace(/[\u1041\u1042\u1043\u1044\u1045\u1046\u1047\u1048\u1049\u1040]/g,function(e){return dn[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return rn[e]})},week:{dow:1,doy:4}}),M.defineLocale("nb",{months:"januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember".split("_"),monthsShort:"jan._feb._mars_apr._mai_juni_juli_aug._sep._okt._nov._des.".split("_"),monthsParseExact:!0,weekdays:"s\xf8ndag_mandag_tirsdag_onsdag_torsdag_fredag_l\xf8rdag".split("_"),weekdaysShort:"s\xf8._ma._ti._on._to._fr._l\xf8.".split("_"),weekdaysMin:"s\xf8_ma_ti_on_to_fr_l\xf8".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY [kl.] HH:mm",LLLL:"dddd D. MMMM YYYY [kl.] HH:mm"},calendar:{sameDay:"[i dag kl.] LT",nextDay:"[i morgen kl.] LT",nextWeek:"dddd [kl.] LT",lastDay:"[i g\xe5r kl.] LT",lastWeek:"[forrige] dddd [kl.] LT",sameElse:"L"},relativeTime:{future:"om %s",past:"%s siden",s:"noen sekunder",ss:"%d sekunder",m:"ett minutt",mm:"%d minutter",h:"en time",hh:"%d timer",d:"en dag",dd:"%d dager",w:"en uke",ww:"%d uker",M:"en m\xe5ned",MM:"%d m\xe5neder",y:"ett \xe5r",yy:"%d \xe5r"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),{1:"\u0967",2:"\u0968",3:"\u0969",4:"\u096a",5:"\u096b",6:"\u096c",7:"\u096d",8:"\u096e",9:"\u096f",0:"\u0966"}),on={"\u0967":"1","\u0968":"2","\u0969":"3","\u096a":"4","\u096b":"5","\u096c":"6","\u096d":"7","\u096e":"8","\u096f":"9","\u0966":"0"},mn=(M.defineLocale("ne",{months:"\u091c\u0928\u0935\u0930\u0940_\u092b\u0947\u092c\u094d\u0930\u0941\u0935\u0930\u0940_\u092e\u093e\u0930\u094d\u091a_\u0905\u092a\u094d\u0930\u093f\u0932_\u092e\u0908_\u091c\u0941\u0928_\u091c\u0941\u0932\u093e\u0908_\u0905\u0917\u0937\u094d\u091f_\u0938\u0947\u092a\u094d\u091f\u0947\u092e\u094d\u092c\u0930_\u0905\u0915\u094d\u091f\u094b\u092c\u0930_\u0928\u094b\u092d\u0947\u092e\u094d\u092c\u0930_\u0921\u093f\u0938\u0947\u092e\u094d\u092c\u0930".split("_"),monthsShort:"\u091c\u0928._\u092b\u0947\u092c\u094d\u0930\u0941._\u092e\u093e\u0930\u094d\u091a_\u0905\u092a\u094d\u0930\u093f._\u092e\u0908_\u091c\u0941\u0928_\u091c\u0941\u0932\u093e\u0908._\u0905\u0917._\u0938\u0947\u092a\u094d\u091f._\u0905\u0915\u094d\u091f\u094b._\u0928\u094b\u092d\u0947._\u0921\u093f\u0938\u0947.".split("_"),monthsParseExact:!0,weekdays:"\u0906\u0907\u0924\u092c\u093e\u0930_\u0938\u094b\u092e\u092c\u093e\u0930_\u092e\u0919\u094d\u0917\u0932\u092c\u093e\u0930_\u092c\u0941\u0927\u092c\u093e\u0930_\u092c\u093f\u0939\u093f\u092c\u093e\u0930_\u0936\u0941\u0915\u094d\u0930\u092c\u093e\u0930_\u0936\u0928\u093f\u092c\u093e\u0930".split("_"),weekdaysShort:"\u0906\u0907\u0924._\u0938\u094b\u092e._\u092e\u0919\u094d\u0917\u0932._\u092c\u0941\u0927._\u092c\u093f\u0939\u093f._\u0936\u0941\u0915\u094d\u0930._\u0936\u0928\u093f.".split("_"),weekdaysMin:"\u0906._\u0938\u094b._\u092e\u0902._\u092c\u0941._\u092c\u093f._\u0936\u0941._\u0936.".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"A\u0915\u094b h:mm \u092c\u091c\u0947",LTS:"A\u0915\u094b h:mm:ss \u092c\u091c\u0947",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A\u0915\u094b h:mm \u092c\u091c\u0947",LLLL:"dddd, D MMMM YYYY, A\u0915\u094b h:mm \u092c\u091c\u0947"},preparse:function(e){return e.replace(/[\u0967\u0968\u0969\u096a\u096b\u096c\u096d\u096e\u096f\u0966]/g,function(e){return on[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return _n[e]})},meridiemParse:/\u0930\u093e\u0924\u093f|\u092c\u093f\u0939\u093e\u0928|\u0926\u093f\u0909\u0901\u0938\u094b|\u0938\u093e\u0901\u091d/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0930\u093e\u0924\u093f"===a?e<4?e:e+12:"\u092c\u093f\u0939\u093e\u0928"===a?e:"\u0926\u093f\u0909\u0901\u0938\u094b"===a?10<=e?e:e+12:"\u0938\u093e\u0901\u091d"===a?e+12:void 0},meridiem:function(e,a,t){return e<3?"\u0930\u093e\u0924\u093f":e<12?"\u092c\u093f\u0939\u093e\u0928":e<16?"\u0926\u093f\u0909\u0901\u0938\u094b":e<20?"\u0938\u093e\u0901\u091d":"\u0930\u093e\u0924\u093f"},calendar:{sameDay:"[\u0906\u091c] LT",nextDay:"[\u092d\u094b\u0932\u093f] LT",nextWeek:"[\u0906\u0909\u0901\u0926\u094b] dddd[,] LT",lastDay:"[\u0939\u093f\u091c\u094b] LT",lastWeek:"[\u0917\u090f\u0915\u094b] dddd[,] LT",sameElse:"L"},relativeTime:{future:"%s\u092e\u093e",past:"%s \u0905\u0917\u093e\u0921\u093f",s:"\u0915\u0947\u0939\u0940 \u0915\u094d\u0937\u0923",ss:"%d \u0938\u0947\u0915\u0947\u0923\u094d\u0921",m:"\u090f\u0915 \u092e\u093f\u0928\u0947\u091f",mm:"%d \u092e\u093f\u0928\u0947\u091f",h:"\u090f\u0915 \u0918\u0923\u094d\u091f\u093e",hh:"%d \u0918\u0923\u094d\u091f\u093e",d:"\u090f\u0915 \u0926\u093f\u0928",dd:"%d \u0926\u093f\u0928",M:"\u090f\u0915 \u092e\u0939\u093f\u0928\u093e",MM:"%d \u092e\u0939\u093f\u0928\u093e",y:"\u090f\u0915 \u092c\u0930\u094d\u0937",yy:"%d \u092c\u0930\u094d\u0937"},week:{dow:0,doy:6}}),"jan._feb._mrt._apr._mei_jun._jul._aug._sep._okt._nov._dec.".split("_")),un="jan_feb_mrt_apr_mei_jun_jul_aug_sep_okt_nov_dec".split("_"),c=[/^jan/i,/^feb/i,/^maart|mrt.?$/i,/^apr/i,/^mei$/i,/^jun[i.]?$/i,/^jul[i.]?$/i,/^aug/i,/^sep/i,/^okt/i,/^nov/i,/^dec/i],L=/^(januari|februari|maart|april|mei|ju[nl]i|augustus|september|oktober|november|december|jan\.?|feb\.?|mrt\.?|apr\.?|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i,ln=(M.defineLocale("nl-be",{months:"januari_februari_maart_april_mei_juni_juli_augustus_september_oktober_november_december".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?un:mn)[e.month()]:mn},monthsRegex:L,monthsShortRegex:L,monthsStrictRegex:/^(januari|februari|maart|april|mei|ju[nl]i|augustus|september|oktober|november|december)/i,monthsShortStrictRegex:/^(jan\.?|feb\.?|mrt\.?|apr\.?|mei|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i,monthsParse:c,longMonthsParse:c,shortMonthsParse:c,weekdays:"zondag_maandag_dinsdag_woensdag_donderdag_vrijdag_zaterdag".split("_"),weekdaysShort:"zo._ma._di._wo._do._vr._za.".split("_"),weekdaysMin:"zo_ma_di_wo_do_vr_za".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[vandaag om] LT",nextDay:"[morgen om] LT",nextWeek:"dddd [om] LT",lastDay:"[gisteren om] LT",lastWeek:"[afgelopen] dddd [om] LT",sameElse:"L"},relativeTime:{future:"over %s",past:"%s geleden",s:"een paar seconden",ss:"%d seconden",m:"\xe9\xe9n minuut",mm:"%d minuten",h:"\xe9\xe9n uur",hh:"%d uur",d:"\xe9\xe9n dag",dd:"%d dagen",M:"\xe9\xe9n maand",MM:"%d maanden",y:"\xe9\xe9n jaar",yy:"%d jaar"},dayOfMonthOrdinalParse:/\d{1,2}(ste|de)/,ordinal:function(e){return e+(1===e||8===e||20<=e?"ste":"de")},week:{dow:1,doy:4}}),"jan._feb._mrt._apr._mei_jun._jul._aug._sep._okt._nov._dec.".split("_")),Mn="jan_feb_mrt_apr_mei_jun_jul_aug_sep_okt_nov_dec".split("_"),f=[/^jan/i,/^feb/i,/^maart|mrt.?$/i,/^apr/i,/^mei$/i,/^jun[i.]?$/i,/^jul[i.]?$/i,/^aug/i,/^sep/i,/^okt/i,/^nov/i,/^dec/i],ke=/^(januari|februari|maart|april|mei|ju[nl]i|augustus|september|oktober|november|december|jan\.?|feb\.?|mrt\.?|apr\.?|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i,hn=(M.defineLocale("nl",{months:"januari_februari_maart_april_mei_juni_juli_augustus_september_oktober_november_december".split("_"),monthsShort:function(e,a){return e?(/-MMM-/.test(a)?Mn:ln)[e.month()]:ln},monthsRegex:ke,monthsShortRegex:ke,monthsStrictRegex:/^(januari|februari|maart|april|mei|ju[nl]i|augustus|september|oktober|november|december)/i,monthsShortStrictRegex:/^(jan\.?|feb\.?|mrt\.?|apr\.?|mei|ju[nl]\.?|aug\.?|sep\.?|okt\.?|nov\.?|dec\.?)/i,monthsParse:f,longMonthsParse:f,shortMonthsParse:f,weekdays:"zondag_maandag_dinsdag_woensdag_donderdag_vrijdag_zaterdag".split("_"),weekdaysShort:"zo._ma._di._wo._do._vr._za.".split("_"),weekdaysMin:"zo_ma_di_wo_do_vr_za".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD-MM-YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[vandaag om] LT",nextDay:"[morgen om] LT",nextWeek:"dddd [om] LT",lastDay:"[gisteren om] LT",lastWeek:"[afgelopen] dddd [om] LT",sameElse:"L"},relativeTime:{future:"over %s",past:"%s geleden",s:"een paar seconden",ss:"%d seconden",m:"\xe9\xe9n minuut",mm:"%d minuten",h:"\xe9\xe9n uur",hh:"%d uur",d:"\xe9\xe9n dag",dd:"%d dagen",w:"\xe9\xe9n week",ww:"%d weken",M:"\xe9\xe9n maand",MM:"%d maanden",y:"\xe9\xe9n jaar",yy:"%d jaar"},dayOfMonthOrdinalParse:/\d{1,2}(ste|de)/,ordinal:function(e){return e+(1===e||8===e||20<=e?"ste":"de")},week:{dow:1,doy:4}}),M.defineLocale("nn",{months:"januar_februar_mars_april_mai_juni_juli_august_september_oktober_november_desember".split("_"),monthsShort:"jan._feb._mars_apr._mai_juni_juli_aug._sep._okt._nov._des.".split("_"),monthsParseExact:!0,weekdays:"sundag_m\xe5ndag_tysdag_onsdag_torsdag_fredag_laurdag".split("_"),weekdaysShort:"su._m\xe5._ty._on._to._fr._lau.".split("_"),weekdaysMin:"su_m\xe5_ty_on_to_fr_la".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY [kl.] H:mm",LLLL:"dddd D. MMMM YYYY [kl.] HH:mm"},calendar:{sameDay:"[I dag klokka] LT",nextDay:"[I morgon klokka] LT",nextWeek:"dddd [klokka] LT",lastDay:"[I g\xe5r klokka] LT",lastWeek:"[F\xf8reg\xe5ande] dddd [klokka] LT",sameElse:"L"},relativeTime:{future:"om %s",past:"%s sidan",s:"nokre sekund",ss:"%d sekund",m:"eit minutt",mm:"%d minutt",h:"ein time",hh:"%d timar",d:"ein dag",dd:"%d dagar",w:"ei veke",ww:"%d veker",M:"ein m\xe5nad",MM:"%d m\xe5nader",y:"eit \xe5r",yy:"%d \xe5r"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("oc-lnc",{months:{standalone:"geni\xe8r_febri\xe8r_mar\xe7_abril_mai_junh_julhet_agost_setembre_oct\xf2bre_novembre_decembre".split("_"),format:"de geni\xe8r_de febri\xe8r_de mar\xe7_d'abril_de mai_de junh_de julhet_d'agost_de setembre_d'oct\xf2bre_de novembre_de decembre".split("_"),isFormat:/D[oD]?(\s)+MMMM/},monthsShort:"gen._febr._mar\xe7_abr._mai_junh_julh._ago._set._oct._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"dimenge_diluns_dimars_dim\xe8cres_dij\xf2us_divendres_dissabte".split("_"),weekdaysShort:"dg._dl._dm._dc._dj._dv._ds.".split("_"),weekdaysMin:"dg_dl_dm_dc_dj_dv_ds".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM [de] YYYY",ll:"D MMM YYYY",LLL:"D MMMM [de] YYYY [a] H:mm",lll:"D MMM YYYY, H:mm",LLLL:"dddd D MMMM [de] YYYY [a] H:mm",llll:"ddd D MMM YYYY, H:mm"},calendar:{sameDay:"[u\xe8i a] LT",nextDay:"[deman a] LT",nextWeek:"dddd [a] LT",lastDay:"[i\xe8r a] LT",lastWeek:"dddd [passat a] LT",sameElse:"L"},relativeTime:{future:"d'aqu\xed %s",past:"fa %s",s:"unas segondas",ss:"%d segondas",m:"una minuta",mm:"%d minutas",h:"una ora",hh:"%d oras",d:"un jorn",dd:"%d jorns",M:"un mes",MM:"%d meses",y:"un an",yy:"%d ans"},dayOfMonthOrdinalParse:/\d{1,2}(r|n|t|\xe8|a)/,ordinal:function(e,a){return e+("w"!==a&&"W"!==a?1===e?"r":2===e?"n":3===e?"r":4===e?"t":"\xe8":"a")},week:{dow:1,doy:4}}),{1:"\u0a67",2:"\u0a68",3:"\u0a69",4:"\u0a6a",5:"\u0a6b",6:"\u0a6c",7:"\u0a6d",8:"\u0a6e",9:"\u0a6f",0:"\u0a66"}),cn={"\u0a67":"1","\u0a68":"2","\u0a69":"3","\u0a6a":"4","\u0a6b":"5","\u0a6c":"6","\u0a6d":"7","\u0a6e":"8","\u0a6f":"9","\u0a66":"0"},Ln=(M.defineLocale("pa-in",{months:"\u0a1c\u0a28\u0a35\u0a30\u0a40_\u0a2b\u0a3c\u0a30\u0a35\u0a30\u0a40_\u0a2e\u0a3e\u0a30\u0a1a_\u0a05\u0a2a\u0a4d\u0a30\u0a48\u0a32_\u0a2e\u0a08_\u0a1c\u0a42\u0a28_\u0a1c\u0a41\u0a32\u0a3e\u0a08_\u0a05\u0a17\u0a38\u0a24_\u0a38\u0a24\u0a70\u0a2c\u0a30_\u0a05\u0a15\u0a24\u0a42\u0a2c\u0a30_\u0a28\u0a35\u0a70\u0a2c\u0a30_\u0a26\u0a38\u0a70\u0a2c\u0a30".split("_"),monthsShort:"\u0a1c\u0a28\u0a35\u0a30\u0a40_\u0a2b\u0a3c\u0a30\u0a35\u0a30\u0a40_\u0a2e\u0a3e\u0a30\u0a1a_\u0a05\u0a2a\u0a4d\u0a30\u0a48\u0a32_\u0a2e\u0a08_\u0a1c\u0a42\u0a28_\u0a1c\u0a41\u0a32\u0a3e\u0a08_\u0a05\u0a17\u0a38\u0a24_\u0a38\u0a24\u0a70\u0a2c\u0a30_\u0a05\u0a15\u0a24\u0a42\u0a2c\u0a30_\u0a28\u0a35\u0a70\u0a2c\u0a30_\u0a26\u0a38\u0a70\u0a2c\u0a30".split("_"),weekdays:"\u0a10\u0a24\u0a35\u0a3e\u0a30_\u0a38\u0a4b\u0a2e\u0a35\u0a3e\u0a30_\u0a2e\u0a70\u0a17\u0a32\u0a35\u0a3e\u0a30_\u0a2c\u0a41\u0a27\u0a35\u0a3e\u0a30_\u0a35\u0a40\u0a30\u0a35\u0a3e\u0a30_\u0a38\u0a3c\u0a41\u0a71\u0a15\u0a30\u0a35\u0a3e\u0a30_\u0a38\u0a3c\u0a28\u0a40\u0a1a\u0a30\u0a35\u0a3e\u0a30".split("_"),weekdaysShort:"\u0a10\u0a24_\u0a38\u0a4b\u0a2e_\u0a2e\u0a70\u0a17\u0a32_\u0a2c\u0a41\u0a27_\u0a35\u0a40\u0a30_\u0a38\u0a3c\u0a41\u0a15\u0a30_\u0a38\u0a3c\u0a28\u0a40".split("_"),weekdaysMin:"\u0a10\u0a24_\u0a38\u0a4b\u0a2e_\u0a2e\u0a70\u0a17\u0a32_\u0a2c\u0a41\u0a27_\u0a35\u0a40\u0a30_\u0a38\u0a3c\u0a41\u0a15\u0a30_\u0a38\u0a3c\u0a28\u0a40".split("_"),longDateFormat:{LT:"A h:mm \u0a35\u0a1c\u0a47",LTS:"A h:mm:ss \u0a35\u0a1c\u0a47",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm \u0a35\u0a1c\u0a47",LLLL:"dddd, D MMMM YYYY, A h:mm \u0a35\u0a1c\u0a47"},calendar:{sameDay:"[\u0a05\u0a1c] LT",nextDay:"[\u0a15\u0a32] LT",nextWeek:"[\u0a05\u0a17\u0a32\u0a3e] dddd, LT",lastDay:"[\u0a15\u0a32] LT",lastWeek:"[\u0a2a\u0a3f\u0a1b\u0a32\u0a47] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0a35\u0a3f\u0a71\u0a1a",past:"%s \u0a2a\u0a3f\u0a1b\u0a32\u0a47",s:"\u0a15\u0a41\u0a1d \u0a38\u0a15\u0a3f\u0a70\u0a1f",ss:"%d \u0a38\u0a15\u0a3f\u0a70\u0a1f",m:"\u0a07\u0a15 \u0a2e\u0a3f\u0a70\u0a1f",mm:"%d \u0a2e\u0a3f\u0a70\u0a1f",h:"\u0a07\u0a71\u0a15 \u0a18\u0a70\u0a1f\u0a3e",hh:"%d \u0a18\u0a70\u0a1f\u0a47",d:"\u0a07\u0a71\u0a15 \u0a26\u0a3f\u0a28",dd:"%d \u0a26\u0a3f\u0a28",M:"\u0a07\u0a71\u0a15 \u0a2e\u0a39\u0a40\u0a28\u0a3e",MM:"%d \u0a2e\u0a39\u0a40\u0a28\u0a47",y:"\u0a07\u0a71\u0a15 \u0a38\u0a3e\u0a32",yy:"%d \u0a38\u0a3e\u0a32"},preparse:function(e){return e.replace(/[\u0a67\u0a68\u0a69\u0a6a\u0a6b\u0a6c\u0a6d\u0a6e\u0a6f\u0a66]/g,function(e){return cn[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return hn[e]})},meridiemParse:/\u0a30\u0a3e\u0a24|\u0a38\u0a35\u0a47\u0a30|\u0a26\u0a41\u0a2a\u0a39\u0a3f\u0a30|\u0a38\u0a3c\u0a3e\u0a2e/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0a30\u0a3e\u0a24"===a?e<4?e:e+12:"\u0a38\u0a35\u0a47\u0a30"===a?e:"\u0a26\u0a41\u0a2a\u0a39\u0a3f\u0a30"===a?10<=e?e:e+12:"\u0a38\u0a3c\u0a3e\u0a2e"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0a30\u0a3e\u0a24":e<10?"\u0a38\u0a35\u0a47\u0a30":e<17?"\u0a26\u0a41\u0a2a\u0a39\u0a3f\u0a30":e<20?"\u0a38\u0a3c\u0a3e\u0a2e":"\u0a30\u0a3e\u0a24"},week:{dow:0,doy:6}}),"stycze\u0144_luty_marzec_kwiecie\u0144_maj_czerwiec_lipiec_sierpie\u0144_wrzesie\u0144_pa\u017adziernik_listopad_grudzie\u0144".split("_")),Yn="stycznia_lutego_marca_kwietnia_maja_czerwca_lipca_sierpnia_wrze\u015bnia_pa\u017adziernika_listopada_grudnia".split("_"),_=[/^sty/i,/^lut/i,/^mar/i,/^kwi/i,/^maj/i,/^cze/i,/^lip/i,/^sie/i,/^wrz/i,/^pa\u017a/i,/^lis/i,/^gru/i];function yn(e){return e%10<5&&1<e%10&&~~(e/10)%10!=1}function fn(e,a,t){var s=e+" ";switch(t){case"ss":return s+(yn(e)?"sekundy":"sekund");case"m":return a?"minuta":"minut\u0119";case"mm":return s+(yn(e)?"minuty":"minut");case"h":return a?"godzina":"godzin\u0119";case"hh":return s+(yn(e)?"godziny":"godzin");case"ww":return s+(yn(e)?"tygodnie":"tygodni");case"MM":return s+(yn(e)?"miesi\u0105ce":"miesi\u0119cy");case"yy":return s+(yn(e)?"lata":"lat")}}function pn(e,a,t){return e+(20<=e%100||100<=e&&e%100==0?" de ":" ")+{ss:"secunde",mm:"minute",hh:"ore",dd:"zile",ww:"s\u0103pt\u0103m\xe2ni",MM:"luni",yy:"ani"}[t]}function kn(e,a,t){return"m"===t?a?"\u043c\u0438\u043d\u0443\u0442\u0430":"\u043c\u0438\u043d\u0443\u0442\u0443":e+" "+(e=+e,a=(a={ss:a?"\u0441\u0435\u043a\u0443\u043d\u0434\u0430_\u0441\u0435\u043a\u0443\u043d\u0434\u044b_\u0441\u0435\u043a\u0443\u043d\u0434":"\u0441\u0435\u043a\u0443\u043d\u0434\u0443_\u0441\u0435\u043a\u0443\u043d\u0434\u044b_\u0441\u0435\u043a\u0443\u043d\u0434",mm:a?"\u043c\u0438\u043d\u0443\u0442\u0430_\u043c\u0438\u043d\u0443\u0442\u044b_\u043c\u0438\u043d\u0443\u0442":"\u043c\u0438\u043d\u0443\u0442\u0443_\u043c\u0438\u043d\u0443\u0442\u044b_\u043c\u0438\u043d\u0443\u0442",hh:"\u0447\u0430\u0441_\u0447\u0430\u0441\u0430_\u0447\u0430\u0441\u043e\u0432",dd:"\u0434\u0435\u043d\u044c_\u0434\u043d\u044f_\u0434\u043d\u0435\u0439",ww:"\u043d\u0435\u0434\u0435\u043b\u044f_\u043d\u0435\u0434\u0435\u043b\u0438_\u043d\u0435\u0434\u0435\u043b\u044c",MM:"\u043c\u0435\u0441\u044f\u0446_\u043c\u0435\u0441\u044f\u0446\u0430_\u043c\u0435\u0441\u044f\u0446\u0435\u0432",yy:"\u0433\u043e\u0434_\u0433\u043e\u0434\u0430_\u043b\u0435\u0442"}[t]).split("_"),e%10==1&&e%100!=11?a[0]:2<=e%10&&e%10<=4&&(e%100<10||20<=e%100)?a[1]:a[2])}M.defineLocale("pl",{months:function(e,a){return e?(/D MMMM/.test(a)?Yn:Ln)[e.month()]:Ln},monthsShort:"sty_lut_mar_kwi_maj_cze_lip_sie_wrz_pa\u017a_lis_gru".split("_"),monthsParse:_,longMonthsParse:_,shortMonthsParse:_,weekdays:"niedziela_poniedzia\u0142ek_wtorek_\u015broda_czwartek_pi\u0105tek_sobota".split("_"),weekdaysShort:"ndz_pon_wt_\u015br_czw_pt_sob".split("_"),weekdaysMin:"Nd_Pn_Wt_\u015ar_Cz_Pt_So".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Dzi\u015b o] LT",nextDay:"[Jutro o] LT",nextWeek:function(){switch(this.day()){case 0:return"[W niedziel\u0119 o] LT";case 2:return"[We wtorek o] LT";case 3:return"[W \u015brod\u0119 o] LT";case 6:return"[W sobot\u0119 o] LT";default:return"[W] dddd [o] LT"}},lastDay:"[Wczoraj o] LT",lastWeek:function(){switch(this.day()){case 0:return"[W zesz\u0142\u0105 niedziel\u0119 o] LT";case 3:return"[W zesz\u0142\u0105 \u015brod\u0119 o] LT";case 6:return"[W zesz\u0142\u0105 sobot\u0119 o] LT";default:return"[W zesz\u0142y] dddd [o] LT"}},sameElse:"L"},relativeTime:{future:"za %s",past:"%s temu",s:"kilka sekund",ss:fn,m:fn,mm:fn,h:fn,hh:fn,d:"1 dzie\u0144",dd:"%d dni",w:"tydzie\u0144",ww:fn,M:"miesi\u0105c",MM:fn,y:"rok",yy:fn},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("pt-br",{months:"janeiro_fevereiro_mar\xe7o_abril_maio_junho_julho_agosto_setembro_outubro_novembro_dezembro".split("_"),monthsShort:"jan_fev_mar_abr_mai_jun_jul_ago_set_out_nov_dez".split("_"),weekdays:"domingo_segunda-feira_ter\xe7a-feira_quarta-feira_quinta-feira_sexta-feira_s\xe1bado".split("_"),weekdaysShort:"dom_seg_ter_qua_qui_sex_s\xe1b".split("_"),weekdaysMin:"do_2\xaa_3\xaa_4\xaa_5\xaa_6\xaa_s\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY [\xe0s] HH:mm",LLLL:"dddd, D [de] MMMM [de] YYYY [\xe0s] HH:mm"},calendar:{sameDay:"[Hoje \xe0s] LT",nextDay:"[Amanh\xe3 \xe0s] LT",nextWeek:"dddd [\xe0s] LT",lastDay:"[Ontem \xe0s] LT",lastWeek:function(){return 0===this.day()||6===this.day()?"[\xdaltimo] dddd [\xe0s] LT":"[\xdaltima] dddd [\xe0s] LT"},sameElse:"L"},relativeTime:{future:"em %s",past:"h\xe1 %s",s:"poucos segundos",ss:"%d segundos",m:"um minuto",mm:"%d minutos",h:"uma hora",hh:"%d horas",d:"um dia",dd:"%d dias",M:"um m\xeas",MM:"%d meses",y:"um ano",yy:"%d anos"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",invalidDate:"Data inv\xe1lida"}),M.defineLocale("pt",{months:"janeiro_fevereiro_mar\xe7o_abril_maio_junho_julho_agosto_setembro_outubro_novembro_dezembro".split("_"),monthsShort:"jan_fev_mar_abr_mai_jun_jul_ago_set_out_nov_dez".split("_"),weekdays:"Domingo_Segunda-feira_Ter\xe7a-feira_Quarta-feira_Quinta-feira_Sexta-feira_S\xe1bado".split("_"),weekdaysShort:"Dom_Seg_Ter_Qua_Qui_Sex_S\xe1b".split("_"),weekdaysMin:"Do_2\xaa_3\xaa_4\xaa_5\xaa_6\xaa_S\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D [de] MMMM [de] YYYY",LLL:"D [de] MMMM [de] YYYY HH:mm",LLLL:"dddd, D [de] MMMM [de] YYYY HH:mm"},calendar:{sameDay:"[Hoje \xe0s] LT",nextDay:"[Amanh\xe3 \xe0s] LT",nextWeek:"dddd [\xe0s] LT",lastDay:"[Ontem \xe0s] LT",lastWeek:function(){return 0===this.day()||6===this.day()?"[\xdaltimo] dddd [\xe0s] LT":"[\xdaltima] dddd [\xe0s] LT"},sameElse:"L"},relativeTime:{future:"em %s",past:"h\xe1 %s",s:"segundos",ss:"%d segundos",m:"um minuto",mm:"%d minutos",h:"uma hora",hh:"%d horas",d:"um dia",dd:"%d dias",w:"uma semana",ww:"%d semanas",M:"um m\xeas",MM:"%d meses",y:"um ano",yy:"%d anos"},dayOfMonthOrdinalParse:/\d{1,2}\xba/,ordinal:"%d\xba",week:{dow:1,doy:4}}),M.defineLocale("ro",{months:"ianuarie_februarie_martie_aprilie_mai_iunie_iulie_august_septembrie_octombrie_noiembrie_decembrie".split("_"),monthsShort:"ian._feb._mart._apr._mai_iun._iul._aug._sept._oct._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"duminic\u0103_luni_mar\u021bi_miercuri_joi_vineri_s\xe2mb\u0103t\u0103".split("_"),weekdaysShort:"Dum_Lun_Mar_Mie_Joi_Vin_S\xe2m".split("_"),weekdaysMin:"Du_Lu_Ma_Mi_Jo_Vi_S\xe2".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY H:mm",LLLL:"dddd, D MMMM YYYY H:mm"},calendar:{sameDay:"[azi la] LT",nextDay:"[m\xe2ine la] LT",nextWeek:"dddd [la] LT",lastDay:"[ieri la] LT",lastWeek:"[fosta] dddd [la] LT",sameElse:"L"},relativeTime:{future:"peste %s",past:"%s \xeen urm\u0103",s:"c\xe2teva secunde",ss:pn,m:"un minut",mm:pn,h:"o or\u0103",hh:pn,d:"o zi",dd:pn,w:"o s\u0103pt\u0103m\xe2n\u0103",ww:pn,M:"o lun\u0103",MM:pn,y:"un an",yy:pn},week:{dow:1,doy:7}});y=[/^\u044f\u043d\u0432/i,/^\u0444\u0435\u0432/i,/^\u043c\u0430\u0440/i,/^\u0430\u043f\u0440/i,/^\u043c\u0430[\u0439\u044f]/i,/^\u0438\u044e\u043d/i,/^\u0438\u044e\u043b/i,/^\u0430\u0432\u0433/i,/^\u0441\u0435\u043d/i,/^\u043e\u043a\u0442/i,/^\u043d\u043e\u044f/i,/^\u0434\u0435\u043a/i],M.defineLocale("ru",{months:{format:"\u044f\u043d\u0432\u0430\u0440\u044f_\u0444\u0435\u0432\u0440\u0430\u043b\u044f_\u043c\u0430\u0440\u0442\u0430_\u0430\u043f\u0440\u0435\u043b\u044f_\u043c\u0430\u044f_\u0438\u044e\u043d\u044f_\u0438\u044e\u043b\u044f_\u0430\u0432\u0433\u0443\u0441\u0442\u0430_\u0441\u0435\u043d\u0442\u044f\u0431\u0440\u044f_\u043e\u043a\u0442\u044f\u0431\u0440\u044f_\u043d\u043e\u044f\u0431\u0440\u044f_\u0434\u0435\u043a\u0430\u0431\u0440\u044f".split("_"),standalone:"\u044f\u043d\u0432\u0430\u0440\u044c_\u0444\u0435\u0432\u0440\u0430\u043b\u044c_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0435\u043b\u044c_\u043c\u0430\u0439_\u0438\u044e\u043d\u044c_\u0438\u044e\u043b\u044c_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043d\u0442\u044f\u0431\u0440\u044c_\u043e\u043a\u0442\u044f\u0431\u0440\u044c_\u043d\u043e\u044f\u0431\u0440\u044c_\u0434\u0435\u043a\u0430\u0431\u0440\u044c".split("_")},monthsShort:{format:"\u044f\u043d\u0432._\u0444\u0435\u0432\u0440._\u043c\u0430\u0440._\u0430\u043f\u0440._\u043c\u0430\u044f_\u0438\u044e\u043d\u044f_\u0438\u044e\u043b\u044f_\u0430\u0432\u0433._\u0441\u0435\u043d\u0442._\u043e\u043a\u0442._\u043d\u043e\u044f\u0431._\u0434\u0435\u043a.".split("_"),standalone:"\u044f\u043d\u0432._\u0444\u0435\u0432\u0440._\u043c\u0430\u0440\u0442_\u0430\u043f\u0440._\u043c\u0430\u0439_\u0438\u044e\u043d\u044c_\u0438\u044e\u043b\u044c_\u0430\u0432\u0433._\u0441\u0435\u043d\u0442._\u043e\u043a\u0442._\u043d\u043e\u044f\u0431._\u0434\u0435\u043a.".split("_")},weekdays:{standalone:"\u0432\u043e\u0441\u043a\u0440\u0435\u0441\u0435\u043d\u044c\u0435_\u043f\u043e\u043d\u0435\u0434\u0435\u043b\u044c\u043d\u0438\u043a_\u0432\u0442\u043e\u0440\u043d\u0438\u043a_\u0441\u0440\u0435\u0434\u0430_\u0447\u0435\u0442\u0432\u0435\u0440\u0433_\u043f\u044f\u0442\u043d\u0438\u0446\u0430_\u0441\u0443\u0431\u0431\u043e\u0442\u0430".split("_"),format:"\u0432\u043e\u0441\u043a\u0440\u0435\u0441\u0435\u043d\u044c\u0435_\u043f\u043e\u043d\u0435\u0434\u0435\u043b\u044c\u043d\u0438\u043a_\u0432\u0442\u043e\u0440\u043d\u0438\u043a_\u0441\u0440\u0435\u0434\u0443_\u0447\u0435\u0442\u0432\u0435\u0440\u0433_\u043f\u044f\u0442\u043d\u0438\u0446\u0443_\u0441\u0443\u0431\u0431\u043e\u0442\u0443".split("_"),isFormat:/\[ ?[\u0412\u0432] ?(?:\u043f\u0440\u043e\u0448\u043b\u0443\u044e|\u0441\u043b\u0435\u0434\u0443\u044e\u0449\u0443\u044e|\u044d\u0442\u0443)? ?] ?dddd/},weekdaysShort:"\u0432\u0441_\u043f\u043d_\u0432\u0442_\u0441\u0440_\u0447\u0442_\u043f\u0442_\u0441\u0431".split("_"),weekdaysMin:"\u0432\u0441_\u043f\u043d_\u0432\u0442_\u0441\u0440_\u0447\u0442_\u043f\u0442_\u0441\u0431".split("_"),monthsParse:y,longMonthsParse:y,shortMonthsParse:y,monthsRegex:/^(\u044f\u043d\u0432\u0430\u0440[\u044c\u044f]|\u044f\u043d\u0432\.?|\u0444\u0435\u0432\u0440\u0430\u043b[\u044c\u044f]|\u0444\u0435\u0432\u0440?\.?|\u043c\u0430\u0440\u0442\u0430?|\u043c\u0430\u0440\.?|\u0430\u043f\u0440\u0435\u043b[\u044c\u044f]|\u0430\u043f\u0440\.?|\u043c\u0430[\u0439\u044f]|\u0438\u044e\u043d[\u044c\u044f]|\u0438\u044e\u043d\.?|\u0438\u044e\u043b[\u044c\u044f]|\u0438\u044e\u043b\.?|\u0430\u0432\u0433\u0443\u0441\u0442\u0430?|\u0430\u0432\u0433\.?|\u0441\u0435\u043d\u0442\u044f\u0431\u0440[\u044c\u044f]|\u0441\u0435\u043d\u0442?\.?|\u043e\u043a\u0442\u044f\u0431\u0440[\u044c\u044f]|\u043e\u043a\u0442\.?|\u043d\u043e\u044f\u0431\u0440[\u044c\u044f]|\u043d\u043e\u044f\u0431?\.?|\u0434\u0435\u043a\u0430\u0431\u0440[\u044c\u044f]|\u0434\u0435\u043a\.?)/i,monthsShortRegex:/^(\u044f\u043d\u0432\u0430\u0440[\u044c\u044f]|\u044f\u043d\u0432\.?|\u0444\u0435\u0432\u0440\u0430\u043b[\u044c\u044f]|\u0444\u0435\u0432\u0440?\.?|\u043c\u0430\u0440\u0442\u0430?|\u043c\u0430\u0440\.?|\u0430\u043f\u0440\u0435\u043b[\u044c\u044f]|\u0430\u043f\u0440\.?|\u043c\u0430[\u0439\u044f]|\u0438\u044e\u043d[\u044c\u044f]|\u0438\u044e\u043d\.?|\u0438\u044e\u043b[\u044c\u044f]|\u0438\u044e\u043b\.?|\u0430\u0432\u0433\u0443\u0441\u0442\u0430?|\u0430\u0432\u0433\.?|\u0441\u0435\u043d\u0442\u044f\u0431\u0440[\u044c\u044f]|\u0441\u0435\u043d\u0442?\.?|\u043e\u043a\u0442\u044f\u0431\u0440[\u044c\u044f]|\u043e\u043a\u0442\.?|\u043d\u043e\u044f\u0431\u0440[\u044c\u044f]|\u043d\u043e\u044f\u0431?\.?|\u0434\u0435\u043a\u0430\u0431\u0440[\u044c\u044f]|\u0434\u0435\u043a\.?)/i,monthsStrictRegex:/^(\u044f\u043d\u0432\u0430\u0440[\u044f\u044c]|\u0444\u0435\u0432\u0440\u0430\u043b[\u044f\u044c]|\u043c\u0430\u0440\u0442\u0430?|\u0430\u043f\u0440\u0435\u043b[\u044f\u044c]|\u043c\u0430[\u044f\u0439]|\u0438\u044e\u043d[\u044f\u044c]|\u0438\u044e\u043b[\u044f\u044c]|\u0430\u0432\u0433\u0443\u0441\u0442\u0430?|\u0441\u0435\u043d\u0442\u044f\u0431\u0440[\u044f\u044c]|\u043e\u043a\u0442\u044f\u0431\u0440[\u044f\u044c]|\u043d\u043e\u044f\u0431\u0440[\u044f\u044c]|\u0434\u0435\u043a\u0430\u0431\u0440[\u044f\u044c])/i,monthsShortStrictRegex:/^(\u044f\u043d\u0432\.|\u0444\u0435\u0432\u0440?\.|\u043c\u0430\u0440[\u0442.]|\u0430\u043f\u0440\.|\u043c\u0430[\u044f\u0439]|\u0438\u044e\u043d[\u044c\u044f.]|\u0438\u044e\u043b[\u044c\u044f.]|\u0430\u0432\u0433\.|\u0441\u0435\u043d\u0442?\.|\u043e\u043a\u0442\.|\u043d\u043e\u044f\u0431?\.|\u0434\u0435\u043a\.)/i,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY \u0433.",LLL:"D MMMM YYYY \u0433., H:mm",LLLL:"dddd, D MMMM YYYY \u0433., H:mm"},calendar:{sameDay:"[\u0421\u0435\u0433\u043e\u0434\u043d\u044f, \u0432] LT",nextDay:"[\u0417\u0430\u0432\u0442\u0440\u0430, \u0432] LT",lastDay:"[\u0412\u0447\u0435\u0440\u0430, \u0432] LT",nextWeek:function(e){if(e.week()===this.week())return 2===this.day()?"[\u0412\u043e] dddd, [\u0432] LT":"[\u0412] dddd, [\u0432] LT";switch(this.day()){case 0:return"[\u0412 \u0441\u043b\u0435\u0434\u0443\u044e\u0449\u0435\u0435] dddd, [\u0432] LT";case 1:case 2:case 4:return"[\u0412 \u0441\u043b\u0435\u0434\u0443\u044e\u0449\u0438\u0439] dddd, [\u0432] LT";case 3:case 5:case 6:return"[\u0412 \u0441\u043b\u0435\u0434\u0443\u044e\u0449\u0443\u044e] dddd, [\u0432] LT"}},lastWeek:function(e){if(e.week()===this.week())return 2===this.day()?"[\u0412\u043e] dddd, [\u0432] LT":"[\u0412] dddd, [\u0432] LT";switch(this.day()){case 0:return"[\u0412 \u043f\u0440\u043e\u0448\u043b\u043e\u0435] dddd, [\u0432] LT";case 1:case 2:case 4:return"[\u0412 \u043f\u0440\u043e\u0448\u043b\u044b\u0439] dddd, [\u0432] LT";case 3:case 5:case 6:return"[\u0412 \u043f\u0440\u043e\u0448\u043b\u0443\u044e] dddd, [\u0432] LT"}},sameElse:"L"},relativeTime:{future:"\u0447\u0435\u0440\u0435\u0437 %s",past:"%s \u043d\u0430\u0437\u0430\u0434",s:"\u043d\u0435\u0441\u043a\u043e\u043b\u044c\u043a\u043e \u0441\u0435\u043a\u0443\u043d\u0434",ss:kn,m:kn,mm:kn,h:"\u0447\u0430\u0441",hh:kn,d:"\u0434\u0435\u043d\u044c",dd:kn,w:"\u043d\u0435\u0434\u0435\u043b\u044f",ww:kn,M:"\u043c\u0435\u0441\u044f\u0446",MM:kn,y:"\u0433\u043e\u0434",yy:kn},meridiemParse:/\u043d\u043e\u0447\u0438|\u0443\u0442\u0440\u0430|\u0434\u043d\u044f|\u0432\u0435\u0447\u0435\u0440\u0430/i,isPM:function(e){return/^(\u0434\u043d\u044f|\u0432\u0435\u0447\u0435\u0440\u0430)$/.test(e)},meridiem:function(e,a,t){return e<4?"\u043d\u043e\u0447\u0438":e<12?"\u0443\u0442\u0440\u0430":e<17?"\u0434\u043d\u044f":"\u0432\u0435\u0447\u0435\u0440\u0430"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0439|\u0433\u043e|\u044f)/,ordinal:function(e,a){switch(a){case"M":case"d":case"DDD":return e+"-\u0439";case"D":return e+"-\u0433\u043e";case"w":case"W":return e+"-\u044f";default:return e}},week:{dow:1,doy:4}}),Y=["\u062c\u0646\u0648\u0631\u064a","\u0641\u064a\u0628\u0631\u0648\u0631\u064a","\u0645\u0627\u0631\u0686","\u0627\u067e\u0631\u064a\u0644","\u0645\u0626\u064a","\u062c\u0648\u0646","\u062c\u0648\u0644\u0627\u0621\u0650","\u0622\u06af\u0633\u067d","\u0633\u064a\u067e\u067d\u0645\u0628\u0631","\u0622\u06aa\u067d\u0648\u0628\u0631","\u0646\u0648\u0645\u0628\u0631","\u068a\u0633\u0645\u0628\u0631"],Ke=["\u0622\u0686\u0631","\u0633\u0648\u0645\u0631","\u0627\u06b1\u0627\u0631\u0648","\u0627\u0631\u0628\u0639","\u062e\u0645\u064a\u0633","\u062c\u0645\u0639","\u0687\u0646\u0687\u0631"],M.defineLocale("sd",{months:Y,monthsShort:Y,weekdays:Ke,weekdaysShort:Ke,weekdaysMin:Ke,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd\u060c D MMMM YYYY HH:mm"},meridiemParse:/\u0635\u0628\u062d|\u0634\u0627\u0645/,isPM:function(e){return"\u0634\u0627\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635\u0628\u062d":"\u0634\u0627\u0645"},calendar:{sameDay:"[\u0627\u0684] LT",nextDay:"[\u0633\u0680\u0627\u06bb\u064a] LT",nextWeek:"dddd [\u0627\u06b3\u064a\u0646 \u0647\u0641\u062a\u064a \u062a\u064a] LT",lastDay:"[\u06aa\u0627\u0644\u0647\u0647] LT",lastWeek:"[\u06af\u0632\u0631\u064a\u0644 \u0647\u0641\u062a\u064a] dddd [\u062a\u064a] LT",sameElse:"L"},relativeTime:{future:"%s \u067e\u0648\u0621",past:"%s \u0627\u06b3",s:"\u0686\u0646\u062f \u0633\u064a\u06aa\u0646\u068a",ss:"%d \u0633\u064a\u06aa\u0646\u068a",m:"\u0647\u06aa \u0645\u0646\u067d",mm:"%d \u0645\u0646\u067d",h:"\u0647\u06aa \u06aa\u0644\u0627\u06aa",hh:"%d \u06aa\u0644\u0627\u06aa",d:"\u0647\u06aa \u068f\u064a\u0646\u0647\u0646",dd:"%d \u068f\u064a\u0646\u0647\u0646",M:"\u0647\u06aa \u0645\u0647\u064a\u0646\u0648",MM:"%d \u0645\u0647\u064a\u0646\u0627",y:"\u0647\u06aa \u0633\u0627\u0644",yy:"%d \u0633\u0627\u0644"},preparse:function(e){return e.replace(/\u060c/g,",")},postformat:function(e){return e.replace(/,/g,"\u060c")},week:{dow:1,doy:4}}),M.defineLocale("se",{months:"o\u0111\u0111ajagem\xe1nnu_guovvam\xe1nnu_njuk\u010dam\xe1nnu_cuo\u014bom\xe1nnu_miessem\xe1nnu_geassem\xe1nnu_suoidnem\xe1nnu_borgem\xe1nnu_\u010dak\u010dam\xe1nnu_golggotm\xe1nnu_sk\xe1bmam\xe1nnu_juovlam\xe1nnu".split("_"),monthsShort:"o\u0111\u0111j_guov_njuk_cuo_mies_geas_suoi_borg_\u010dak\u010d_golg_sk\xe1b_juov".split("_"),weekdays:"sotnabeaivi_vuoss\xe1rga_ma\u014b\u014beb\xe1rga_gaskavahkku_duorastat_bearjadat_l\xe1vvardat".split("_"),weekdaysShort:"sotn_vuos_ma\u014b_gask_duor_bear_l\xe1v".split("_"),weekdaysMin:"s_v_m_g_d_b_L".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"MMMM D. [b.] YYYY",LLL:"MMMM D. [b.] YYYY [ti.] HH:mm",LLLL:"dddd, MMMM D. [b.] YYYY [ti.] HH:mm"},calendar:{sameDay:"[otne ti] LT",nextDay:"[ihttin ti] LT",nextWeek:"dddd [ti] LT",lastDay:"[ikte ti] LT",lastWeek:"[ovddit] dddd [ti] LT",sameElse:"L"},relativeTime:{future:"%s gea\u017ees",past:"ma\u014bit %s",s:"moadde sekunddat",ss:"%d sekunddat",m:"okta minuhta",mm:"%d minuhtat",h:"okta diimmu",hh:"%d diimmut",d:"okta beaivi",dd:"%d beaivvit",M:"okta m\xe1nnu",MM:"%d m\xe1nut",y:"okta jahki",yy:"%d jagit"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("si",{months:"\u0da2\u0db1\u0dc0\u0dcf\u0dbb\u0dd2_\u0db4\u0dd9\u0db6\u0dbb\u0dc0\u0dcf\u0dbb\u0dd2_\u0db8\u0dcf\u0dbb\u0dca\u0dad\u0dd4_\u0d85\u0db4\u0dca\u200d\u0dbb\u0dda\u0dbd\u0dca_\u0db8\u0dd0\u0dba\u0dd2_\u0da2\u0dd6\u0db1\u0dd2_\u0da2\u0dd6\u0dbd\u0dd2_\u0d85\u0d9c\u0ddd\u0dc3\u0dca\u0dad\u0dd4_\u0dc3\u0dd0\u0db4\u0dca\u0dad\u0dd0\u0db8\u0dca\u0db6\u0dbb\u0dca_\u0d94\u0d9a\u0dca\u0dad\u0ddd\u0db6\u0dbb\u0dca_\u0db1\u0ddc\u0dc0\u0dd0\u0db8\u0dca\u0db6\u0dbb\u0dca_\u0daf\u0dd9\u0dc3\u0dd0\u0db8\u0dca\u0db6\u0dbb\u0dca".split("_"),monthsShort:"\u0da2\u0db1_\u0db4\u0dd9\u0db6_\u0db8\u0dcf\u0dbb\u0dca_\u0d85\u0db4\u0dca_\u0db8\u0dd0\u0dba\u0dd2_\u0da2\u0dd6\u0db1\u0dd2_\u0da2\u0dd6\u0dbd\u0dd2_\u0d85\u0d9c\u0ddd_\u0dc3\u0dd0\u0db4\u0dca_\u0d94\u0d9a\u0dca_\u0db1\u0ddc\u0dc0\u0dd0_\u0daf\u0dd9\u0dc3\u0dd0".split("_"),weekdays:"\u0d89\u0dbb\u0dd2\u0daf\u0dcf_\u0dc3\u0db3\u0dd4\u0daf\u0dcf_\u0d85\u0d9f\u0dc4\u0dbb\u0dd4\u0dc0\u0dcf\u0daf\u0dcf_\u0db6\u0daf\u0dcf\u0daf\u0dcf_\u0db6\u0dca\u200d\u0dbb\u0dc4\u0dc3\u0dca\u0db4\u0dad\u0dd2\u0db1\u0dca\u0daf\u0dcf_\u0dc3\u0dd2\u0d9a\u0dd4\u0dbb\u0dcf\u0daf\u0dcf_\u0dc3\u0dd9\u0db1\u0dc3\u0dd4\u0dbb\u0dcf\u0daf\u0dcf".split("_"),weekdaysShort:"\u0d89\u0dbb\u0dd2_\u0dc3\u0db3\u0dd4_\u0d85\u0d9f_\u0db6\u0daf\u0dcf_\u0db6\u0dca\u200d\u0dbb\u0dc4_\u0dc3\u0dd2\u0d9a\u0dd4_\u0dc3\u0dd9\u0db1".split("_"),weekdaysMin:"\u0d89_\u0dc3_\u0d85_\u0db6_\u0db6\u0dca\u200d\u0dbb_\u0dc3\u0dd2_\u0dc3\u0dd9".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"a h:mm",LTS:"a h:mm:ss",L:"YYYY/MM/DD",LL:"YYYY MMMM D",LLL:"YYYY MMMM D, a h:mm",LLLL:"YYYY MMMM D [\u0dc0\u0dd0\u0db1\u0dd2] dddd, a h:mm:ss"},calendar:{sameDay:"[\u0d85\u0daf] LT[\u0da7]",nextDay:"[\u0dc4\u0dd9\u0da7] LT[\u0da7]",nextWeek:"dddd LT[\u0da7]",lastDay:"[\u0d8a\u0dba\u0dda] LT[\u0da7]",lastWeek:"[\u0db4\u0dc3\u0dd4\u0d9c\u0dd2\u0dba] dddd LT[\u0da7]",sameElse:"L"},relativeTime:{future:"%s\u0d9a\u0dd2\u0db1\u0dca",past:"%s\u0d9a\u0da7 \u0db4\u0dd9\u0dbb",s:"\u0dad\u0dad\u0dca\u0db4\u0dbb \u0d9a\u0dd2\u0dc4\u0dd2\u0db4\u0dba",ss:"\u0dad\u0dad\u0dca\u0db4\u0dbb %d",m:"\u0db8\u0dd2\u0db1\u0dd2\u0dad\u0dca\u0dad\u0dd4\u0dc0",mm:"\u0db8\u0dd2\u0db1\u0dd2\u0dad\u0dca\u0dad\u0dd4 %d",h:"\u0db4\u0dd0\u0dba",hh:"\u0db4\u0dd0\u0dba %d",d:"\u0daf\u0dd2\u0db1\u0dba",dd:"\u0daf\u0dd2\u0db1 %d",M:"\u0db8\u0dcf\u0dc3\u0dba",MM:"\u0db8\u0dcf\u0dc3 %d",y:"\u0dc0\u0dc3\u0dbb",yy:"\u0dc0\u0dc3\u0dbb %d"},dayOfMonthOrdinalParse:/\d{1,2} \u0dc0\u0dd0\u0db1\u0dd2/,ordinal:function(e){return e+" \u0dc0\u0dd0\u0db1\u0dd2"},meridiemParse:/\u0db4\u0dd9\u0dbb \u0dc0\u0dbb\u0dd4|\u0db4\u0dc3\u0dca \u0dc0\u0dbb\u0dd4|\u0db4\u0dd9.\u0dc0|\u0db4.\u0dc0./,isPM:function(e){return"\u0db4.\u0dc0."===e||"\u0db4\u0dc3\u0dca \u0dc0\u0dbb\u0dd4"===e},meridiem:function(e,a,t){return 11<e?t?"\u0db4.\u0dc0.":"\u0db4\u0dc3\u0dca \u0dc0\u0dbb\u0dd4":t?"\u0db4\u0dd9.\u0dc0.":"\u0db4\u0dd9\u0dbb \u0dc0\u0dbb\u0dd4"}}),Ht="janu\xe1r_febru\xe1r_marec_apr\xedl_m\xe1j_j\xfan_j\xfal_august_september_okt\xf3ber_november_december".split("_"),r="jan_feb_mar_apr_m\xe1j_j\xfan_j\xfal_aug_sep_okt_nov_dec".split("_");function Dn(e){return 1<e&&e<5}function Tn(e,a,t,s){var n=e+" ";switch(t){case"s":return a||s?"p\xe1r sek\xfand":"p\xe1r sekundami";case"ss":return a||s?n+(Dn(e)?"sekundy":"sek\xfand"):n+"sekundami";case"m":return a?"min\xfata":s?"min\xfatu":"min\xfatou";case"mm":return a||s?n+(Dn(e)?"min\xfaty":"min\xfat"):n+"min\xfatami";case"h":return a?"hodina":s?"hodinu":"hodinou";case"hh":return a||s?n+(Dn(e)?"hodiny":"hod\xedn"):n+"hodinami";case"d":return a||s?"de\u0148":"d\u0148om";case"dd":return a||s?n+(Dn(e)?"dni":"dn\xed"):n+"d\u0148ami";case"M":return a||s?"mesiac":"mesiacom";case"MM":return a||s?n+(Dn(e)?"mesiace":"mesiacov"):n+"mesiacmi";case"y":return a||s?"rok":"rokom";case"yy":return a||s?n+(Dn(e)?"roky":"rokov"):n+"rokmi"}}function gn(e,a,t,s){var n=e+" ";switch(t){case"s":return a||s?"nekaj sekund":"nekaj sekundami";case"ss":return n+=1===e?a?"sekundo":"sekundi":2===e?a||s?"sekundi":"sekundah":e<5?a||s?"sekunde":"sekundah":"sekund";case"m":return a?"ena minuta":"eno minuto";case"mm":return n+=1===e?a?"minuta":"minuto":2===e?a||s?"minuti":"minutama":e<5?a||s?"minute":"minutami":a||s?"minut":"minutami";case"h":return a?"ena ura":"eno uro";case"hh":return n+=1===e?a?"ura":"uro":2===e?a||s?"uri":"urama":e<5?a||s?"ure":"urami":a||s?"ur":"urami";case"d":return a||s?"en dan":"enim dnem";case"dd":return n+=1===e?a||s?"dan":"dnem":2===e?a||s?"dni":"dnevoma":a||s?"dni":"dnevi";case"M":return a||s?"en mesec":"enim mesecem";case"MM":return n+=1===e?a||s?"mesec":"mesecem":2===e?a||s?"meseca":"mesecema":e<5?a||s?"mesece":"meseci":a||s?"mesecev":"meseci";case"y":return a||s?"eno leto":"enim letom";case"yy":return n+=1===e?a||s?"leto":"letom":2===e?a||s?"leti":"letoma":e<5?a||s?"leta":"leti":a||s?"let":"leti"}}M.defineLocale("sk",{months:Ht,monthsShort:r,weekdays:"nede\u013ea_pondelok_utorok_streda_\u0161tvrtok_piatok_sobota".split("_"),weekdaysShort:"ne_po_ut_st_\u0161t_pi_so".split("_"),weekdaysMin:"ne_po_ut_st_\u0161t_pi_so".split("_"),longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD.MM.YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd D. MMMM YYYY H:mm"},calendar:{sameDay:"[dnes o] LT",nextDay:"[zajtra o] LT",nextWeek:function(){switch(this.day()){case 0:return"[v nede\u013eu o] LT";case 1:case 2:return"[v] dddd [o] LT";case 3:return"[v stredu o] LT";case 4:return"[vo \u0161tvrtok o] LT";case 5:return"[v piatok o] LT";case 6:return"[v sobotu o] LT"}},lastDay:"[v\u010dera o] LT",lastWeek:function(){switch(this.day()){case 0:return"[minul\xfa nede\u013eu o] LT";case 1:case 2:return"[minul\xfd] dddd [o] LT";case 3:return"[minul\xfa stredu o] LT";case 4:case 5:return"[minul\xfd] dddd [o] LT";case 6:return"[minul\xfa sobotu o] LT"}},sameElse:"L"},relativeTime:{future:"za %s",past:"pred %s",s:Tn,ss:Tn,m:Tn,mm:Tn,h:Tn,hh:Tn,d:Tn,dd:Tn,M:Tn,MM:Tn,y:Tn,yy:Tn},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("sl",{months:"januar_februar_marec_april_maj_junij_julij_avgust_september_oktober_november_december".split("_"),monthsShort:"jan._feb._mar._apr._maj._jun._jul._avg._sep._okt._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"nedelja_ponedeljek_torek_sreda_\u010detrtek_petek_sobota".split("_"),weekdaysShort:"ned._pon._tor._sre._\u010det._pet._sob.".split("_"),weekdaysMin:"ne_po_to_sr_\u010de_pe_so".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD. MM. YYYY",LL:"D. MMMM YYYY",LLL:"D. MMMM YYYY H:mm",LLLL:"dddd, D. MMMM YYYY H:mm"},calendar:{sameDay:"[danes ob] LT",nextDay:"[jutri ob] LT",nextWeek:function(){switch(this.day()){case 0:return"[v] [nedeljo] [ob] LT";case 3:return"[v] [sredo] [ob] LT";case 6:return"[v] [soboto] [ob] LT";case 1:case 2:case 4:case 5:return"[v] dddd [ob] LT"}},lastDay:"[v\u010deraj ob] LT",lastWeek:function(){switch(this.day()){case 0:return"[prej\u0161njo] [nedeljo] [ob] LT";case 3:return"[prej\u0161njo] [sredo] [ob] LT";case 6:return"[prej\u0161njo] [soboto] [ob] LT";case 1:case 2:case 4:case 5:return"[prej\u0161nji] dddd [ob] LT"}},sameElse:"L"},relativeTime:{future:"\u010dez %s",past:"pred %s",s:gn,ss:gn,m:gn,mm:gn,h:gn,hh:gn,d:gn,dd:gn,M:gn,MM:gn,y:gn,yy:gn},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}}),M.defineLocale("sq",{months:"Janar_Shkurt_Mars_Prill_Maj_Qershor_Korrik_Gusht_Shtator_Tetor_N\xebntor_Dhjetor".split("_"),monthsShort:"Jan_Shk_Mar_Pri_Maj_Qer_Kor_Gus_Sht_Tet_N\xebn_Dhj".split("_"),weekdays:"E Diel_E H\xebn\xeb_E Mart\xeb_E M\xebrkur\xeb_E Enjte_E Premte_E Shtun\xeb".split("_"),weekdaysShort:"Die_H\xebn_Mar_M\xebr_Enj_Pre_Sht".split("_"),weekdaysMin:"D_H_Ma_M\xeb_E_P_Sh".split("_"),weekdaysParseExact:!0,meridiemParse:/PD|MD/,isPM:function(e){return"M"===e.charAt(0)},meridiem:function(e,a,t){return e<12?"PD":"MD"},longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Sot n\xeb] LT",nextDay:"[Nes\xebr n\xeb] LT",nextWeek:"dddd [n\xeb] LT",lastDay:"[Dje n\xeb] LT",lastWeek:"dddd [e kaluar n\xeb] LT",sameElse:"L"},relativeTime:{future:"n\xeb %s",past:"%s m\xeb par\xeb",s:"disa sekonda",ss:"%d sekonda",m:"nj\xeb minut\xeb",mm:"%d minuta",h:"nj\xeb or\xeb",hh:"%d or\xeb",d:"nj\xeb dit\xeb",dd:"%d dit\xeb",M:"nj\xeb muaj",MM:"%d muaj",y:"nj\xeb vit",yy:"%d vite"},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}});var W={words:{ss:["\u0441\u0435\u043a\u0443\u043d\u0434\u0430","\u0441\u0435\u043a\u0443\u043d\u0434\u0435","\u0441\u0435\u043a\u0443\u043d\u0434\u0438"],m:["\u0458\u0435\u0434\u0430\u043d \u043c\u0438\u043d\u0443\u0442","\u0458\u0435\u0434\u043d\u043e\u0433 \u043c\u0438\u043d\u0443\u0442\u0430"],mm:["\u043c\u0438\u043d\u0443\u0442","\u043c\u0438\u043d\u0443\u0442\u0430","\u043c\u0438\u043d\u0443\u0442\u0430"],h:["\u0458\u0435\u0434\u0430\u043d \u0441\u0430\u0442","\u0458\u0435\u0434\u043d\u043e\u0433 \u0441\u0430\u0442\u0430"],hh:["\u0441\u0430\u0442","\u0441\u0430\u0442\u0430","\u0441\u0430\u0442\u0438"],d:["\u0458\u0435\u0434\u0430\u043d \u0434\u0430\u043d","\u0458\u0435\u0434\u043d\u043e\u0433 \u0434\u0430\u043d\u0430"],dd:["\u0434\u0430\u043d","\u0434\u0430\u043d\u0430","\u0434\u0430\u043d\u0430"],M:["\u0458\u0435\u0434\u0430\u043d \u043c\u0435\u0441\u0435\u0446","\u0458\u0435\u0434\u043d\u043e\u0433 \u043c\u0435\u0441\u0435\u0446\u0430"],MM:["\u043c\u0435\u0441\u0435\u0446","\u043c\u0435\u0441\u0435\u0446\u0430","\u043c\u0435\u0441\u0435\u0446\u0438"],y:["\u0458\u0435\u0434\u043d\u0443 \u0433\u043e\u0434\u0438\u043d\u0443","\u0458\u0435\u0434\u043d\u0435 \u0433\u043e\u0434\u0438\u043d\u0435"],yy:["\u0433\u043e\u0434\u0438\u043d\u0443","\u0433\u043e\u0434\u0438\u043d\u0435","\u0433\u043e\u0434\u0438\u043d\u0430"]},correctGrammaticalCase:function(e,a){return 1<=e%10&&e%10<=4&&(e%100<10||20<=e%100)?e%10==1?a[0]:a[1]:a[2]},translate:function(e,a,t,s){var n=W.words[t];if(1===t.length)return"y"===t&&a?"\u0458\u0435\u0434\u043d\u0430 \u0433\u043e\u0434\u0438\u043d\u0430":s||a?n[0]:n[1];s=W.correctGrammaticalCase(e,n);return"yy"===t&&a&&"\u0433\u043e\u0434\u0438\u043d\u0443"===s?e+" \u0433\u043e\u0434\u0438\u043d\u0430":e+" "+s}},A=(M.defineLocale("sr-cyrl",{months:"\u0458\u0430\u043d\u0443\u0430\u0440_\u0444\u0435\u0431\u0440\u0443\u0430\u0440_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0438\u043b_\u043c\u0430\u0458_\u0458\u0443\u043d_\u0458\u0443\u043b_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043f\u0442\u0435\u043c\u0431\u0430\u0440_\u043e\u043a\u0442\u043e\u0431\u0430\u0440_\u043d\u043e\u0432\u0435\u043c\u0431\u0430\u0440_\u0434\u0435\u0446\u0435\u043c\u0431\u0430\u0440".split("_"),monthsShort:"\u0458\u0430\u043d._\u0444\u0435\u0431._\u043c\u0430\u0440._\u0430\u043f\u0440._\u043c\u0430\u0458_\u0458\u0443\u043d_\u0458\u0443\u043b_\u0430\u0432\u0433._\u0441\u0435\u043f._\u043e\u043a\u0442._\u043d\u043e\u0432._\u0434\u0435\u0446.".split("_"),monthsParseExact:!0,weekdays:"\u043d\u0435\u0434\u0435\u0459\u0430_\u043f\u043e\u043d\u0435\u0434\u0435\u0459\u0430\u043a_\u0443\u0442\u043e\u0440\u0430\u043a_\u0441\u0440\u0435\u0434\u0430_\u0447\u0435\u0442\u0432\u0440\u0442\u0430\u043a_\u043f\u0435\u0442\u0430\u043a_\u0441\u0443\u0431\u043e\u0442\u0430".split("_"),weekdaysShort:"\u043d\u0435\u0434._\u043f\u043e\u043d._\u0443\u0442\u043e._\u0441\u0440\u0435._\u0447\u0435\u0442._\u043f\u0435\u0442._\u0441\u0443\u0431.".split("_"),weekdaysMin:"\u043d\u0435_\u043f\u043e_\u0443\u0442_\u0441\u0440_\u0447\u0435_\u043f\u0435_\u0441\u0443".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"D. M. YYYY.",LL:"D. MMMM YYYY.",LLL:"D. MMMM YYYY. H:mm",LLLL:"dddd, D. MMMM YYYY. H:mm"},calendar:{sameDay:"[\u0434\u0430\u043d\u0430\u0441 \u0443] LT",nextDay:"[\u0441\u0443\u0442\u0440\u0430 \u0443] LT",nextWeek:function(){switch(this.day()){case 0:return"[\u0443] [\u043d\u0435\u0434\u0435\u0459\u0443] [\u0443] LT";case 3:return"[\u0443] [\u0441\u0440\u0435\u0434\u0443] [\u0443] LT";case 6:return"[\u0443] [\u0441\u0443\u0431\u043e\u0442\u0443] [\u0443] LT";case 1:case 2:case 4:case 5:return"[\u0443] dddd [\u0443] LT"}},lastDay:"[\u0458\u0443\u0447\u0435 \u0443] LT",lastWeek:function(){return["[\u043f\u0440\u043e\u0448\u043b\u0435] [\u043d\u0435\u0434\u0435\u0459\u0435] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u043e\u0433] [\u043f\u043e\u043d\u0435\u0434\u0435\u0459\u043a\u0430] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u043e\u0433] [\u0443\u0442\u043e\u0440\u043a\u0430] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u0435] [\u0441\u0440\u0435\u0434\u0435] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u043e\u0433] [\u0447\u0435\u0442\u0432\u0440\u0442\u043a\u0430] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u043e\u0433] [\u043f\u0435\u0442\u043a\u0430] [\u0443] LT","[\u043f\u0440\u043e\u0448\u043b\u0435] [\u0441\u0443\u0431\u043e\u0442\u0435] [\u0443] LT"][this.day()]},sameElse:"L"},relativeTime:{future:"\u0437\u0430 %s",past:"\u043f\u0440\u0435 %s",s:"\u043d\u0435\u043a\u043e\u043b\u0438\u043a\u043e \u0441\u0435\u043a\u0443\u043d\u0434\u0438",ss:W.translate,m:W.translate,mm:W.translate,h:W.translate,hh:W.translate,d:W.translate,dd:W.translate,M:W.translate,MM:W.translate,y:W.translate,yy:W.translate},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}}),{words:{ss:["sekunda","sekunde","sekundi"],m:["jedan minut","jednog minuta"],mm:["minut","minuta","minuta"],h:["jedan sat","jednog sata"],hh:["sat","sata","sati"],d:["jedan dan","jednog dana"],dd:["dan","dana","dana"],M:["jedan mesec","jednog meseca"],MM:["mesec","meseca","meseci"],y:["jednu godinu","jedne godine"],yy:["godinu","godine","godina"]},correctGrammaticalCase:function(e,a){return 1<=e%10&&e%10<=4&&(e%100<10||20<=e%100)?e%10==1?a[0]:a[1]:a[2]},translate:function(e,a,t,s){var n=A.words[t];if(1===t.length)return"y"===t&&a?"jedna godina":s||a?n[0]:n[1];s=A.correctGrammaticalCase(e,n);return"yy"===t&&a&&"godinu"===s?e+" godina":e+" "+s}}),wn=(M.defineLocale("sr",{months:"januar_februar_mart_april_maj_jun_jul_avgust_septembar_oktobar_novembar_decembar".split("_"),monthsShort:"jan._feb._mar._apr._maj_jun_jul_avg._sep._okt._nov._dec.".split("_"),monthsParseExact:!0,weekdays:"nedelja_ponedeljak_utorak_sreda_\u010detvrtak_petak_subota".split("_"),weekdaysShort:"ned._pon._uto._sre._\u010det._pet._sub.".split("_"),weekdaysMin:"ne_po_ut_sr_\u010de_pe_su".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"D. M. YYYY.",LL:"D. MMMM YYYY.",LLL:"D. MMMM YYYY. H:mm",LLLL:"dddd, D. MMMM YYYY. H:mm"},calendar:{sameDay:"[danas u] LT",nextDay:"[sutra u] LT",nextWeek:function(){switch(this.day()){case 0:return"[u] [nedelju] [u] LT";case 3:return"[u] [sredu] [u] LT";case 6:return"[u] [subotu] [u] LT";case 1:case 2:case 4:case 5:return"[u] dddd [u] LT"}},lastDay:"[ju\u010de u] LT",lastWeek:function(){return["[pro\u0161le] [nedelje] [u] LT","[pro\u0161log] [ponedeljka] [u] LT","[pro\u0161log] [utorka] [u] LT","[pro\u0161le] [srede] [u] LT","[pro\u0161log] [\u010detvrtka] [u] LT","[pro\u0161log] [petka] [u] LT","[pro\u0161le] [subote] [u] LT"][this.day()]},sameElse:"L"},relativeTime:{future:"za %s",past:"pre %s",s:"nekoliko sekundi",ss:A.translate,m:A.translate,mm:A.translate,h:A.translate,hh:A.translate,d:A.translate,dd:A.translate,M:A.translate,MM:A.translate,y:A.translate,yy:A.translate},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:7}}),M.defineLocale("ss",{months:"Bhimbidvwane_Indlovana_Indlov'lenkhulu_Mabasa_Inkhwekhweti_Inhlaba_Kholwane_Ingci_Inyoni_Imphala_Lweti_Ingongoni".split("_"),monthsShort:"Bhi_Ina_Inu_Mab_Ink_Inh_Kho_Igc_Iny_Imp_Lwe_Igo".split("_"),weekdays:"Lisontfo_Umsombuluko_Lesibili_Lesitsatfu_Lesine_Lesihlanu_Umgcibelo".split("_"),weekdaysShort:"Lis_Umb_Lsb_Les_Lsi_Lsh_Umg".split("_"),weekdaysMin:"Li_Us_Lb_Lt_Ls_Lh_Ug".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendar:{sameDay:"[Namuhla nga] LT",nextDay:"[Kusasa nga] LT",nextWeek:"dddd [nga] LT",lastDay:"[Itolo nga] LT",lastWeek:"dddd [leliphelile] [nga] LT",sameElse:"L"},relativeTime:{future:"nga %s",past:"wenteka nga %s",s:"emizuzwana lomcane",ss:"%d mzuzwana",m:"umzuzu",mm:"%d emizuzu",h:"lihora",hh:"%d emahora",d:"lilanga",dd:"%d emalanga",M:"inyanga",MM:"%d tinyanga",y:"umnyaka",yy:"%d iminyaka"},meridiemParse:/ekuseni|emini|entsambama|ebusuku/,meridiem:function(e,a,t){return e<11?"ekuseni":e<15?"emini":e<19?"entsambama":"ebusuku"},meridiemHour:function(e,a){return 12===e&&(e=0),"ekuseni"===a?e:"emini"===a?11<=e?e:e+12:"entsambama"===a||"ebusuku"===a?0===e?0:e+12:void 0},dayOfMonthOrdinalParse:/\d{1,2}/,ordinal:"%d",week:{dow:1,doy:4}}),M.defineLocale("sv",{months:"januari_februari_mars_april_maj_juni_juli_augusti_september_oktober_november_december".split("_"),monthsShort:"jan_feb_mar_apr_maj_jun_jul_aug_sep_okt_nov_dec".split("_"),weekdays:"s\xf6ndag_m\xe5ndag_tisdag_onsdag_torsdag_fredag_l\xf6rdag".split("_"),weekdaysShort:"s\xf6n_m\xe5n_tis_ons_tor_fre_l\xf6r".split("_"),weekdaysMin:"s\xf6_m\xe5_ti_on_to_fr_l\xf6".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"D MMMM YYYY",LLL:"D MMMM YYYY [kl.] HH:mm",LLLL:"dddd D MMMM YYYY [kl.] HH:mm",lll:"D MMM YYYY HH:mm",llll:"ddd D MMM YYYY HH:mm"},calendar:{sameDay:"[Idag] LT",nextDay:"[Imorgon] LT",lastDay:"[Ig\xe5r] LT",nextWeek:"[P\xe5] dddd LT",lastWeek:"[I] dddd[s] LT",sameElse:"L"},relativeTime:{future:"om %s",past:"f\xf6r %s sedan",s:"n\xe5gra sekunder",ss:"%d sekunder",m:"en minut",mm:"%d minuter",h:"en timme",hh:"%d timmar",d:"en dag",dd:"%d dagar",M:"en m\xe5nad",MM:"%d m\xe5nader",y:"ett \xe5r",yy:"%d \xe5r"},dayOfMonthOrdinalParse:/\d{1,2}(\:e|\:a)/,ordinal:function(e){var a=e%10;return e+(1!=~~(e%100/10)&&(1==a||2==a)?":a":":e")},week:{dow:1,doy:4}}),M.defineLocale("sw",{months:"Januari_Februari_Machi_Aprili_Mei_Juni_Julai_Agosti_Septemba_Oktoba_Novemba_Desemba".split("_"),monthsShort:"Jan_Feb_Mac_Apr_Mei_Jun_Jul_Ago_Sep_Okt_Nov_Des".split("_"),weekdays:"Jumapili_Jumatatu_Jumanne_Jumatano_Alhamisi_Ijumaa_Jumamosi".split("_"),weekdaysShort:"Jpl_Jtat_Jnne_Jtan_Alh_Ijm_Jmos".split("_"),weekdaysMin:"J2_J3_J4_J5_Al_Ij_J1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"hh:mm A",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[leo saa] LT",nextDay:"[kesho saa] LT",nextWeek:"[wiki ijayo] dddd [saat] LT",lastDay:"[jana] LT",lastWeek:"[wiki iliyopita] dddd [saat] LT",sameElse:"L"},relativeTime:{future:"%s baadaye",past:"tokea %s",s:"hivi punde",ss:"sekunde %d",m:"dakika moja",mm:"dakika %d",h:"saa limoja",hh:"masaa %d",d:"siku moja",dd:"siku %d",M:"mwezi mmoja",MM:"miezi %d",y:"mwaka mmoja",yy:"miaka %d"},week:{dow:1,doy:7}}),{1:"\u0be7",2:"\u0be8",3:"\u0be9",4:"\u0bea",5:"\u0beb",6:"\u0bec",7:"\u0bed",8:"\u0bee",9:"\u0bef",0:"\u0be6"}),bn={"\u0be7":"1","\u0be8":"2","\u0be9":"3","\u0bea":"4","\u0beb":"5","\u0bec":"6","\u0bed":"7","\u0bee":"8","\u0bef":"9","\u0be6":"0"},Sn=(M.defineLocale("ta",{months:"\u0b9c\u0ba9\u0bb5\u0bb0\u0bbf_\u0baa\u0bbf\u0baa\u0bcd\u0bb0\u0bb5\u0bb0\u0bbf_\u0bae\u0bbe\u0bb0\u0bcd\u0b9a\u0bcd_\u0b8f\u0baa\u0bcd\u0bb0\u0bb2\u0bcd_\u0bae\u0bc7_\u0b9c\u0bc2\u0ba9\u0bcd_\u0b9c\u0bc2\u0bb2\u0bc8_\u0b86\u0b95\u0bb8\u0bcd\u0b9f\u0bcd_\u0b9a\u0bc6\u0baa\u0bcd\u0b9f\u0bc6\u0bae\u0bcd\u0baa\u0bb0\u0bcd_\u0b85\u0b95\u0bcd\u0b9f\u0bc7\u0bbe\u0baa\u0bb0\u0bcd_\u0ba8\u0bb5\u0bae\u0bcd\u0baa\u0bb0\u0bcd_\u0b9f\u0bbf\u0b9a\u0bae\u0bcd\u0baa\u0bb0\u0bcd".split("_"),monthsShort:"\u0b9c\u0ba9\u0bb5\u0bb0\u0bbf_\u0baa\u0bbf\u0baa\u0bcd\u0bb0\u0bb5\u0bb0\u0bbf_\u0bae\u0bbe\u0bb0\u0bcd\u0b9a\u0bcd_\u0b8f\u0baa\u0bcd\u0bb0\u0bb2\u0bcd_\u0bae\u0bc7_\u0b9c\u0bc2\u0ba9\u0bcd_\u0b9c\u0bc2\u0bb2\u0bc8_\u0b86\u0b95\u0bb8\u0bcd\u0b9f\u0bcd_\u0b9a\u0bc6\u0baa\u0bcd\u0b9f\u0bc6\u0bae\u0bcd\u0baa\u0bb0\u0bcd_\u0b85\u0b95\u0bcd\u0b9f\u0bc7\u0bbe\u0baa\u0bb0\u0bcd_\u0ba8\u0bb5\u0bae\u0bcd\u0baa\u0bb0\u0bcd_\u0b9f\u0bbf\u0b9a\u0bae\u0bcd\u0baa\u0bb0\u0bcd".split("_"),weekdays:"\u0b9e\u0bbe\u0baf\u0bbf\u0bb1\u0bcd\u0bb1\u0bc1\u0b95\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0ba4\u0bbf\u0b99\u0bcd\u0b95\u0b9f\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0b9a\u0bc6\u0bb5\u0bcd\u0bb5\u0bbe\u0baf\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0baa\u0bc1\u0ba4\u0ba9\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0bb5\u0bbf\u0baf\u0bbe\u0bb4\u0b95\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0bb5\u0bc6\u0bb3\u0bcd\u0bb3\u0bbf\u0b95\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8_\u0b9a\u0ba9\u0bbf\u0b95\u0bcd\u0b95\u0bbf\u0bb4\u0bae\u0bc8".split("_"),weekdaysShort:"\u0b9e\u0bbe\u0baf\u0bbf\u0bb1\u0bc1_\u0ba4\u0bbf\u0b99\u0bcd\u0b95\u0bb3\u0bcd_\u0b9a\u0bc6\u0bb5\u0bcd\u0bb5\u0bbe\u0baf\u0bcd_\u0baa\u0bc1\u0ba4\u0ba9\u0bcd_\u0bb5\u0bbf\u0baf\u0bbe\u0bb4\u0ba9\u0bcd_\u0bb5\u0bc6\u0bb3\u0bcd\u0bb3\u0bbf_\u0b9a\u0ba9\u0bbf".split("_"),weekdaysMin:"\u0b9e\u0bbe_\u0ba4\u0bbf_\u0b9a\u0bc6_\u0baa\u0bc1_\u0bb5\u0bbf_\u0bb5\u0bc6_\u0b9a".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, HH:mm",LLLL:"dddd, D MMMM YYYY, HH:mm"},calendar:{sameDay:"[\u0b87\u0ba9\u0bcd\u0bb1\u0bc1] LT",nextDay:"[\u0ba8\u0bbe\u0bb3\u0bc8] LT",nextWeek:"dddd, LT",lastDay:"[\u0ba8\u0bc7\u0bb1\u0bcd\u0bb1\u0bc1] LT",lastWeek:"[\u0b95\u0b9f\u0ba8\u0bcd\u0ba4 \u0bb5\u0bbe\u0bb0\u0bae\u0bcd] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0b87\u0bb2\u0bcd",past:"%s \u0bae\u0bc1\u0ba9\u0bcd",s:"\u0b92\u0bb0\u0bc1 \u0b9a\u0bbf\u0bb2 \u0bb5\u0bbf\u0ba8\u0bbe\u0b9f\u0bbf\u0b95\u0bb3\u0bcd",ss:"%d \u0bb5\u0bbf\u0ba8\u0bbe\u0b9f\u0bbf\u0b95\u0bb3\u0bcd",m:"\u0b92\u0bb0\u0bc1 \u0ba8\u0bbf\u0bae\u0bbf\u0b9f\u0bae\u0bcd",mm:"%d \u0ba8\u0bbf\u0bae\u0bbf\u0b9f\u0b99\u0bcd\u0b95\u0bb3\u0bcd",h:"\u0b92\u0bb0\u0bc1 \u0bae\u0ba3\u0bbf \u0ba8\u0bc7\u0bb0\u0bae\u0bcd",hh:"%d \u0bae\u0ba3\u0bbf \u0ba8\u0bc7\u0bb0\u0bae\u0bcd",d:"\u0b92\u0bb0\u0bc1 \u0ba8\u0bbe\u0bb3\u0bcd",dd:"%d \u0ba8\u0bbe\u0b9f\u0bcd\u0b95\u0bb3\u0bcd",M:"\u0b92\u0bb0\u0bc1 \u0bae\u0bbe\u0ba4\u0bae\u0bcd",MM:"%d \u0bae\u0bbe\u0ba4\u0b99\u0bcd\u0b95\u0bb3\u0bcd",y:"\u0b92\u0bb0\u0bc1 \u0bb5\u0bb0\u0bc1\u0b9f\u0bae\u0bcd",yy:"%d \u0b86\u0ba3\u0bcd\u0b9f\u0bc1\u0b95\u0bb3\u0bcd"},dayOfMonthOrdinalParse:/\d{1,2}\u0bb5\u0ba4\u0bc1/,ordinal:function(e){return e+"\u0bb5\u0ba4\u0bc1"},preparse:function(e){return e.replace(/[\u0be7\u0be8\u0be9\u0bea\u0beb\u0bec\u0bed\u0bee\u0bef\u0be6]/g,function(e){return bn[e]})},postformat:function(e){return e.replace(/\d/g,function(e){return wn[e]})},meridiemParse:/\u0baf\u0bbe\u0bae\u0bae\u0bcd|\u0bb5\u0bc8\u0b95\u0bb1\u0bc8|\u0b95\u0bbe\u0bb2\u0bc8|\u0ba8\u0ba3\u0bcd\u0baa\u0b95\u0bb2\u0bcd|\u0b8e\u0bb1\u0bcd\u0baa\u0bbe\u0b9f\u0bc1|\u0bae\u0bbe\u0bb2\u0bc8/,meridiem:function(e,a,t){return e<2?" \u0baf\u0bbe\u0bae\u0bae\u0bcd":e<6?" \u0bb5\u0bc8\u0b95\u0bb1\u0bc8":e<10?" \u0b95\u0bbe\u0bb2\u0bc8":e<14?" \u0ba8\u0ba3\u0bcd\u0baa\u0b95\u0bb2\u0bcd":e<18?" \u0b8e\u0bb1\u0bcd\u0baa\u0bbe\u0b9f\u0bc1":e<22?" \u0bae\u0bbe\u0bb2\u0bc8":" \u0baf\u0bbe\u0bae\u0bae\u0bcd"},meridiemHour:function(e,a){return 12===e&&(e=0),"\u0baf\u0bbe\u0bae\u0bae\u0bcd"===a?e<2?e:e+12:"\u0bb5\u0bc8\u0b95\u0bb1\u0bc8"===a||"\u0b95\u0bbe\u0bb2\u0bc8"===a||"\u0ba8\u0ba3\u0bcd\u0baa\u0b95\u0bb2\u0bcd"===a&&10<=e?e:e+12},week:{dow:0,doy:6}}),M.defineLocale("te",{months:"\u0c1c\u0c28\u0c35\u0c30\u0c3f_\u0c2b\u0c3f\u0c2c\u0c4d\u0c30\u0c35\u0c30\u0c3f_\u0c2e\u0c3e\u0c30\u0c4d\u0c1a\u0c3f_\u0c0f\u0c2a\u0c4d\u0c30\u0c3f\u0c32\u0c4d_\u0c2e\u0c47_\u0c1c\u0c42\u0c28\u0c4d_\u0c1c\u0c41\u0c32\u0c48_\u0c06\u0c17\u0c38\u0c4d\u0c1f\u0c41_\u0c38\u0c46\u0c2a\u0c4d\u0c1f\u0c46\u0c02\u0c2c\u0c30\u0c4d_\u0c05\u0c15\u0c4d\u0c1f\u0c4b\u0c2c\u0c30\u0c4d_\u0c28\u0c35\u0c02\u0c2c\u0c30\u0c4d_\u0c21\u0c3f\u0c38\u0c46\u0c02\u0c2c\u0c30\u0c4d".split("_"),monthsShort:"\u0c1c\u0c28._\u0c2b\u0c3f\u0c2c\u0c4d\u0c30._\u0c2e\u0c3e\u0c30\u0c4d\u0c1a\u0c3f_\u0c0f\u0c2a\u0c4d\u0c30\u0c3f._\u0c2e\u0c47_\u0c1c\u0c42\u0c28\u0c4d_\u0c1c\u0c41\u0c32\u0c48_\u0c06\u0c17._\u0c38\u0c46\u0c2a\u0c4d._\u0c05\u0c15\u0c4d\u0c1f\u0c4b._\u0c28\u0c35._\u0c21\u0c3f\u0c38\u0c46.".split("_"),monthsParseExact:!0,weekdays:"\u0c06\u0c26\u0c3f\u0c35\u0c3e\u0c30\u0c02_\u0c38\u0c4b\u0c2e\u0c35\u0c3e\u0c30\u0c02_\u0c2e\u0c02\u0c17\u0c33\u0c35\u0c3e\u0c30\u0c02_\u0c2c\u0c41\u0c27\u0c35\u0c3e\u0c30\u0c02_\u0c17\u0c41\u0c30\u0c41\u0c35\u0c3e\u0c30\u0c02_\u0c36\u0c41\u0c15\u0c4d\u0c30\u0c35\u0c3e\u0c30\u0c02_\u0c36\u0c28\u0c3f\u0c35\u0c3e\u0c30\u0c02".split("_"),weekdaysShort:"\u0c06\u0c26\u0c3f_\u0c38\u0c4b\u0c2e_\u0c2e\u0c02\u0c17\u0c33_\u0c2c\u0c41\u0c27_\u0c17\u0c41\u0c30\u0c41_\u0c36\u0c41\u0c15\u0c4d\u0c30_\u0c36\u0c28\u0c3f".split("_"),weekdaysMin:"\u0c06_\u0c38\u0c4b_\u0c2e\u0c02_\u0c2c\u0c41_\u0c17\u0c41_\u0c36\u0c41_\u0c36".split("_"),longDateFormat:{LT:"A h:mm",LTS:"A h:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY, A h:mm",LLLL:"dddd, D MMMM YYYY, A h:mm"},calendar:{sameDay:"[\u0c28\u0c47\u0c21\u0c41] LT",nextDay:"[\u0c30\u0c47\u0c2a\u0c41] LT",nextWeek:"dddd, LT",lastDay:"[\u0c28\u0c3f\u0c28\u0c4d\u0c28] LT",lastWeek:"[\u0c17\u0c24] dddd, LT",sameElse:"L"},relativeTime:{future:"%s \u0c32\u0c4b",past:"%s \u0c15\u0c4d\u0c30\u0c3f\u0c24\u0c02",s:"\u0c15\u0c4a\u0c28\u0c4d\u0c28\u0c3f \u0c15\u0c4d\u0c37\u0c23\u0c3e\u0c32\u0c41",ss:"%d \u0c38\u0c46\u0c15\u0c28\u0c4d\u0c32\u0c41",m:"\u0c12\u0c15 \u0c28\u0c3f\u0c2e\u0c3f\u0c37\u0c02",mm:"%d \u0c28\u0c3f\u0c2e\u0c3f\u0c37\u0c3e\u0c32\u0c41",h:"\u0c12\u0c15 \u0c17\u0c02\u0c1f",hh:"%d \u0c17\u0c02\u0c1f\u0c32\u0c41",d:"\u0c12\u0c15 \u0c30\u0c4b\u0c1c\u0c41",dd:"%d \u0c30\u0c4b\u0c1c\u0c41\u0c32\u0c41",M:"\u0c12\u0c15 \u0c28\u0c46\u0c32",MM:"%d \u0c28\u0c46\u0c32\u0c32\u0c41",y:"\u0c12\u0c15 \u0c38\u0c02\u0c35\u0c24\u0c4d\u0c38\u0c30\u0c02",yy:"%d \u0c38\u0c02\u0c35\u0c24\u0c4d\u0c38\u0c30\u0c3e\u0c32\u0c41"},dayOfMonthOrdinalParse:/\d{1,2}\u0c35/,ordinal:"%d\u0c35",meridiemParse:/\u0c30\u0c3e\u0c24\u0c4d\u0c30\u0c3f|\u0c09\u0c26\u0c2f\u0c02|\u0c2e\u0c27\u0c4d\u0c2f\u0c3e\u0c39\u0c4d\u0c28\u0c02|\u0c38\u0c3e\u0c2f\u0c02\u0c24\u0c4d\u0c30\u0c02/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0c30\u0c3e\u0c24\u0c4d\u0c30\u0c3f"===a?e<4?e:e+12:"\u0c09\u0c26\u0c2f\u0c02"===a?e:"\u0c2e\u0c27\u0c4d\u0c2f\u0c3e\u0c39\u0c4d\u0c28\u0c02"===a?10<=e?e:e+12:"\u0c38\u0c3e\u0c2f\u0c02\u0c24\u0c4d\u0c30\u0c02"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0c30\u0c3e\u0c24\u0c4d\u0c30\u0c3f":e<10?"\u0c09\u0c26\u0c2f\u0c02":e<17?"\u0c2e\u0c27\u0c4d\u0c2f\u0c3e\u0c39\u0c4d\u0c28\u0c02":e<20?"\u0c38\u0c3e\u0c2f\u0c02\u0c24\u0c4d\u0c30\u0c02":"\u0c30\u0c3e\u0c24\u0c4d\u0c30\u0c3f"},week:{dow:0,doy:6}}),M.defineLocale("tet",{months:"Janeiru_Fevereiru_Marsu_Abril_Maiu_Ju\xf1u_Jullu_Agustu_Setembru_Outubru_Novembru_Dezembru".split("_"),monthsShort:"Jan_Fev_Mar_Abr_Mai_Jun_Jul_Ago_Set_Out_Nov_Dez".split("_"),weekdays:"Domingu_Segunda_Tersa_Kuarta_Kinta_Sesta_Sabadu".split("_"),weekdaysShort:"Dom_Seg_Ters_Kua_Kint_Sest_Sab".split("_"),weekdaysMin:"Do_Seg_Te_Ku_Ki_Ses_Sa".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[Ohin iha] LT",nextDay:"[Aban iha] LT",nextWeek:"dddd [iha] LT",lastDay:"[Horiseik iha] LT",lastWeek:"dddd [semana kotuk] [iha] LT",sameElse:"L"},relativeTime:{future:"iha %s",past:"%s liuba",s:"segundu balun",ss:"segundu %d",m:"minutu ida",mm:"minutu %d",h:"oras ida",hh:"oras %d",d:"loron ida",dd:"loron %d",M:"fulan ida",MM:"fulan %d",y:"tinan ida",yy:"tinan %d"},dayOfMonthOrdinalParse:/\d{1,2}(st|nd|rd|th)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),{0:"-\u0443\u043c",1:"-\u0443\u043c",2:"-\u044e\u043c",3:"-\u044e\u043c",4:"-\u0443\u043c",5:"-\u0443\u043c",6:"-\u0443\u043c",7:"-\u0443\u043c",8:"-\u0443\u043c",9:"-\u0443\u043c",10:"-\u0443\u043c",12:"-\u0443\u043c",13:"-\u0443\u043c",20:"-\u0443\u043c",30:"-\u044e\u043c",40:"-\u0443\u043c",50:"-\u0443\u043c",60:"-\u0443\u043c",70:"-\u0443\u043c",80:"-\u0443\u043c",90:"-\u0443\u043c",100:"-\u0443\u043c"}),Hn=(M.defineLocale("tg",{months:{format:"\u044f\u043d\u0432\u0430\u0440\u0438_\u0444\u0435\u0432\u0440\u0430\u043b\u0438_\u043c\u0430\u0440\u0442\u0438_\u0430\u043f\u0440\u0435\u043b\u0438_\u043c\u0430\u0439\u0438_\u0438\u044e\u043d\u0438_\u0438\u044e\u043b\u0438_\u0430\u0432\u0433\u0443\u0441\u0442\u0438_\u0441\u0435\u043d\u0442\u044f\u0431\u0440\u0438_\u043e\u043a\u0442\u044f\u0431\u0440\u0438_\u043d\u043e\u044f\u0431\u0440\u0438_\u0434\u0435\u043a\u0430\u0431\u0440\u0438".split("_"),standalone:"\u044f\u043d\u0432\u0430\u0440_\u0444\u0435\u0432\u0440\u0430\u043b_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0435\u043b_\u043c\u0430\u0439_\u0438\u044e\u043d_\u0438\u044e\u043b_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043d\u0442\u044f\u0431\u0440_\u043e\u043a\u0442\u044f\u0431\u0440_\u043d\u043e\u044f\u0431\u0440_\u0434\u0435\u043a\u0430\u0431\u0440".split("_")},monthsShort:"\u044f\u043d\u0432_\u0444\u0435\u0432_\u043c\u0430\u0440_\u0430\u043f\u0440_\u043c\u0430\u0439_\u0438\u044e\u043d_\u0438\u044e\u043b_\u0430\u0432\u0433_\u0441\u0435\u043d_\u043e\u043a\u0442_\u043d\u043e\u044f_\u0434\u0435\u043a".split("_"),weekdays:"\u044f\u043a\u0448\u0430\u043d\u0431\u0435_\u0434\u0443\u0448\u0430\u043d\u0431\u0435_\u0441\u0435\u0448\u0430\u043d\u0431\u0435_\u0447\u043e\u0440\u0448\u0430\u043d\u0431\u0435_\u043f\u0430\u043d\u04b7\u0448\u0430\u043d\u0431\u0435_\u04b7\u0443\u043c\u044a\u0430_\u0448\u0430\u043d\u0431\u0435".split("_"),weekdaysShort:"\u044f\u0448\u0431_\u0434\u0448\u0431_\u0441\u0448\u0431_\u0447\u0448\u0431_\u043f\u0448\u0431_\u04b7\u0443\u043c_\u0448\u043d\u0431".split("_"),weekdaysMin:"\u044f\u0448_\u0434\u0448_\u0441\u0448_\u0447\u0448_\u043f\u0448_\u04b7\u043c_\u0448\u0431".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u0418\u043c\u0440\u04ef\u0437 \u0441\u043e\u0430\u0442\u0438] LT",nextDay:"[\u0424\u0430\u0440\u0434\u043e \u0441\u043e\u0430\u0442\u0438] LT",lastDay:"[\u0414\u0438\u0440\u04ef\u0437 \u0441\u043e\u0430\u0442\u0438] LT",nextWeek:"dddd[\u0438] [\u04b3\u0430\u0444\u0442\u0430\u0438 \u043e\u044f\u043d\u0434\u0430 \u0441\u043e\u0430\u0442\u0438] LT",lastWeek:"dddd[\u0438] [\u04b3\u0430\u0444\u0442\u0430\u0438 \u0433\u0443\u0437\u0430\u0448\u0442\u0430 \u0441\u043e\u0430\u0442\u0438] LT",sameElse:"L"},relativeTime:{future:"\u0431\u0430\u044a\u0434\u0438 %s",past:"%s \u043f\u0435\u0448",s:"\u044f\u043a\u0447\u0430\u043d\u0434 \u0441\u043e\u043d\u0438\u044f",m:"\u044f\u043a \u0434\u0430\u049b\u0438\u049b\u0430",mm:"%d \u0434\u0430\u049b\u0438\u049b\u0430",h:"\u044f\u043a \u0441\u043e\u0430\u0442",hh:"%d \u0441\u043e\u0430\u0442",d:"\u044f\u043a \u0440\u04ef\u0437",dd:"%d \u0440\u04ef\u0437",M:"\u044f\u043a \u043c\u043e\u04b3",MM:"%d \u043c\u043e\u04b3",y:"\u044f\u043a \u0441\u043e\u043b",yy:"%d \u0441\u043e\u043b"},meridiemParse:/\u0448\u0430\u0431|\u0441\u0443\u0431\u04b3|\u0440\u04ef\u0437|\u0431\u0435\u0433\u043e\u04b3/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u0448\u0430\u0431"===a?e<4?e:e+12:"\u0441\u0443\u0431\u04b3"===a?e:"\u0440\u04ef\u0437"===a?11<=e?e:e+12:"\u0431\u0435\u0433\u043e\u04b3"===a?e+12:void 0},meridiem:function(e,a,t){return e<4?"\u0448\u0430\u0431":e<11?"\u0441\u0443\u0431\u04b3":e<16?"\u0440\u04ef\u0437":e<19?"\u0431\u0435\u0433\u043e\u04b3":"\u0448\u0430\u0431"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0443\u043c|\u044e\u043c)/,ordinal:function(e){return e+(Sn[e]||Sn[e%10]||Sn[100<=e?100:null])},week:{dow:1,doy:7}}),M.defineLocale("th",{months:"\u0e21\u0e01\u0e23\u0e32\u0e04\u0e21_\u0e01\u0e38\u0e21\u0e20\u0e32\u0e1e\u0e31\u0e19\u0e18\u0e4c_\u0e21\u0e35\u0e19\u0e32\u0e04\u0e21_\u0e40\u0e21\u0e29\u0e32\u0e22\u0e19_\u0e1e\u0e24\u0e29\u0e20\u0e32\u0e04\u0e21_\u0e21\u0e34\u0e16\u0e38\u0e19\u0e32\u0e22\u0e19_\u0e01\u0e23\u0e01\u0e0e\u0e32\u0e04\u0e21_\u0e2a\u0e34\u0e07\u0e2b\u0e32\u0e04\u0e21_\u0e01\u0e31\u0e19\u0e22\u0e32\u0e22\u0e19_\u0e15\u0e38\u0e25\u0e32\u0e04\u0e21_\u0e1e\u0e24\u0e28\u0e08\u0e34\u0e01\u0e32\u0e22\u0e19_\u0e18\u0e31\u0e19\u0e27\u0e32\u0e04\u0e21".split("_"),monthsShort:"\u0e21.\u0e04._\u0e01.\u0e1e._\u0e21\u0e35.\u0e04._\u0e40\u0e21.\u0e22._\u0e1e.\u0e04._\u0e21\u0e34.\u0e22._\u0e01.\u0e04._\u0e2a.\u0e04._\u0e01.\u0e22._\u0e15.\u0e04._\u0e1e.\u0e22._\u0e18.\u0e04.".split("_"),monthsParseExact:!0,weekdays:"\u0e2d\u0e32\u0e17\u0e34\u0e15\u0e22\u0e4c_\u0e08\u0e31\u0e19\u0e17\u0e23\u0e4c_\u0e2d\u0e31\u0e07\u0e04\u0e32\u0e23_\u0e1e\u0e38\u0e18_\u0e1e\u0e24\u0e2b\u0e31\u0e2a\u0e1a\u0e14\u0e35_\u0e28\u0e38\u0e01\u0e23\u0e4c_\u0e40\u0e2a\u0e32\u0e23\u0e4c".split("_"),weekdaysShort:"\u0e2d\u0e32\u0e17\u0e34\u0e15\u0e22\u0e4c_\u0e08\u0e31\u0e19\u0e17\u0e23\u0e4c_\u0e2d\u0e31\u0e07\u0e04\u0e32\u0e23_\u0e1e\u0e38\u0e18_\u0e1e\u0e24\u0e2b\u0e31\u0e2a_\u0e28\u0e38\u0e01\u0e23\u0e4c_\u0e40\u0e2a\u0e32\u0e23\u0e4c".split("_"),weekdaysMin:"\u0e2d\u0e32._\u0e08._\u0e2d._\u0e1e._\u0e1e\u0e24._\u0e28._\u0e2a.".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"H:mm",LTS:"H:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY \u0e40\u0e27\u0e25\u0e32 H:mm",LLLL:"\u0e27\u0e31\u0e19dddd\u0e17\u0e35\u0e48 D MMMM YYYY \u0e40\u0e27\u0e25\u0e32 H:mm"},meridiemParse:/\u0e01\u0e48\u0e2d\u0e19\u0e40\u0e17\u0e35\u0e48\u0e22\u0e07|\u0e2b\u0e25\u0e31\u0e07\u0e40\u0e17\u0e35\u0e48\u0e22\u0e07/,isPM:function(e){return"\u0e2b\u0e25\u0e31\u0e07\u0e40\u0e17\u0e35\u0e48\u0e22\u0e07"===e},meridiem:function(e,a,t){return e<12?"\u0e01\u0e48\u0e2d\u0e19\u0e40\u0e17\u0e35\u0e48\u0e22\u0e07":"\u0e2b\u0e25\u0e31\u0e07\u0e40\u0e17\u0e35\u0e48\u0e22\u0e07"},calendar:{sameDay:"[\u0e27\u0e31\u0e19\u0e19\u0e35\u0e49 \u0e40\u0e27\u0e25\u0e32] LT",nextDay:"[\u0e1e\u0e23\u0e38\u0e48\u0e07\u0e19\u0e35\u0e49 \u0e40\u0e27\u0e25\u0e32] LT",nextWeek:"dddd[\u0e2b\u0e19\u0e49\u0e32 \u0e40\u0e27\u0e25\u0e32] LT",lastDay:"[\u0e40\u0e21\u0e37\u0e48\u0e2d\u0e27\u0e32\u0e19\u0e19\u0e35\u0e49 \u0e40\u0e27\u0e25\u0e32] LT",lastWeek:"[\u0e27\u0e31\u0e19]dddd[\u0e17\u0e35\u0e48\u0e41\u0e25\u0e49\u0e27 \u0e40\u0e27\u0e25\u0e32] LT",sameElse:"L"},relativeTime:{future:"\u0e2d\u0e35\u0e01 %s",past:"%s\u0e17\u0e35\u0e48\u0e41\u0e25\u0e49\u0e27",s:"\u0e44\u0e21\u0e48\u0e01\u0e35\u0e48\u0e27\u0e34\u0e19\u0e32\u0e17\u0e35",ss:"%d \u0e27\u0e34\u0e19\u0e32\u0e17\u0e35",m:"1 \u0e19\u0e32\u0e17\u0e35",mm:"%d \u0e19\u0e32\u0e17\u0e35",h:"1 \u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07",hh:"%d \u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07",d:"1 \u0e27\u0e31\u0e19",dd:"%d \u0e27\u0e31\u0e19",w:"1 \u0e2a\u0e31\u0e1b\u0e14\u0e32\u0e2b\u0e4c",ww:"%d \u0e2a\u0e31\u0e1b\u0e14\u0e32\u0e2b\u0e4c",M:"1 \u0e40\u0e14\u0e37\u0e2d\u0e19",MM:"%d \u0e40\u0e14\u0e37\u0e2d\u0e19",y:"1 \u0e1b\u0e35",yy:"%d \u0e1b\u0e35"}}),{1:"'inji",5:"'inji",8:"'inji",70:"'inji",80:"'inji",2:"'nji",7:"'nji",20:"'nji",50:"'nji",3:"'\xfcnji",4:"'\xfcnji",100:"'\xfcnji",6:"'njy",9:"'unjy",10:"'unjy",30:"'unjy",60:"'ynjy",90:"'ynjy"}),vn=(M.defineLocale("tk",{months:"\xddanwar_Fewral_Mart_Aprel_Ma\xfd_I\xfdun_I\xfdul_Awgust_Sent\xfdabr_Okt\xfdabr_No\xfdabr_Dekabr".split("_"),monthsShort:"\xddan_Few_Mar_Apr_Ma\xfd_I\xfdn_I\xfdl_Awg_Sen_Okt_No\xfd_Dek".split("_"),weekdays:"\xddek\u015fenbe_Du\u015fenbe_Si\u015fenbe_\xc7ar\u015fenbe_Pen\u015fenbe_Anna_\u015eenbe".split("_"),weekdaysShort:"\xddek_Du\u015f_Si\u015f_\xc7ar_Pen_Ann_\u015een".split("_"),weekdaysMin:"\xddk_D\u015f_S\u015f_\xc7r_Pn_An_\u015en".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[bug\xfcn sagat] LT",nextDay:"[ertir sagat] LT",nextWeek:"[indiki] dddd [sagat] LT",lastDay:"[d\xfc\xfdn] LT",lastWeek:"[ge\xe7en] dddd [sagat] LT",sameElse:"L"},relativeTime:{future:"%s so\u0148",past:"%s \xf6\u0148",s:"birn\xe4\xe7e sekunt",m:"bir minut",mm:"%d minut",h:"bir sagat",hh:"%d sagat",d:"bir g\xfcn",dd:"%d g\xfcn",M:"bir a\xfd",MM:"%d a\xfd",y:"bir \xfdyl",yy:"%d \xfdyl"},ordinal:function(e,a){switch(a){case"d":case"D":case"Do":case"DD":return e;default:if(0===e)return e+"'unjy";var t=e%10;return e+(Hn[t]||Hn[e%100-t]||Hn[100<=e?100:null])}},week:{dow:1,doy:7}}),M.defineLocale("tl-ph",{months:"Enero_Pebrero_Marso_Abril_Mayo_Hunyo_Hulyo_Agosto_Setyembre_Oktubre_Nobyembre_Disyembre".split("_"),monthsShort:"Ene_Peb_Mar_Abr_May_Hun_Hul_Ago_Set_Okt_Nob_Dis".split("_"),weekdays:"Linggo_Lunes_Martes_Miyerkules_Huwebes_Biyernes_Sabado".split("_"),weekdaysShort:"Lin_Lun_Mar_Miy_Huw_Biy_Sab".split("_"),weekdaysMin:"Li_Lu_Ma_Mi_Hu_Bi_Sab".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"MM/D/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY HH:mm",LLLL:"dddd, MMMM DD, YYYY HH:mm"},calendar:{sameDay:"LT [ngayong araw]",nextDay:"[Bukas ng] LT",nextWeek:"LT [sa susunod na] dddd",lastDay:"LT [kahapon]",lastWeek:"LT [noong nakaraang] dddd",sameElse:"L"},relativeTime:{future:"sa loob ng %s",past:"%s ang nakalipas",s:"ilang segundo",ss:"%d segundo",m:"isang minuto",mm:"%d minuto",h:"isang oras",hh:"%d oras",d:"isang araw",dd:"%d araw",M:"isang buwan",MM:"%d buwan",y:"isang taon",yy:"%d taon"},dayOfMonthOrdinalParse:/\d{1,2}/,ordinal:function(e){return e},week:{dow:1,doy:4}}),"pagh_wa\u2019_cha\u2019_wej_loS_vagh_jav_Soch_chorgh_Hut".split("_"));function jn(e,a,t,s){var n=function(e){var a=Math.floor(e%1e3/100),t=Math.floor(e%100/10),e=e%10,s="";0<a&&(s+=vn[a]+"vatlh");0<t&&(s+=(""!==s?" ":"")+vn[t]+"maH");0<e&&(s+=(""!==s?" ":"")+vn[e]);return""===s?"pagh":s}(e);switch(t){case"ss":return n+" lup";case"mm":return n+" tup";case"hh":return n+" rep";case"dd":return n+" jaj";case"MM":return n+" jar";case"yy":return n+" DIS"}}M.defineLocale("tlh",{months:"tera\u2019 jar wa\u2019_tera\u2019 jar cha\u2019_tera\u2019 jar wej_tera\u2019 jar loS_tera\u2019 jar vagh_tera\u2019 jar jav_tera\u2019 jar Soch_tera\u2019 jar chorgh_tera\u2019 jar Hut_tera\u2019 jar wa\u2019maH_tera\u2019 jar wa\u2019maH wa\u2019_tera\u2019 jar wa\u2019maH cha\u2019".split("_"),monthsShort:"jar wa\u2019_jar cha\u2019_jar wej_jar loS_jar vagh_jar jav_jar Soch_jar chorgh_jar Hut_jar wa\u2019maH_jar wa\u2019maH wa\u2019_jar wa\u2019maH cha\u2019".split("_"),monthsParseExact:!0,weekdays:"lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"),weekdaysShort:"lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"),weekdaysMin:"lojmItjaj_DaSjaj_povjaj_ghItlhjaj_loghjaj_buqjaj_ghInjaj".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[DaHjaj] LT",nextDay:"[wa\u2019leS] LT",nextWeek:"LLL",lastDay:"[wa\u2019Hu\u2019] LT",lastWeek:"LLL",sameElse:"L"},relativeTime:{future:function(e){var a=e;return a=-1!==e.indexOf("jaj")?a.slice(0,-3)+"leS":-1!==e.indexOf("jar")?a.slice(0,-3)+"waQ":-1!==e.indexOf("DIS")?a.slice(0,-3)+"nem":a+" pIq"},past:function(e){var a=e;return a=-1!==e.indexOf("jaj")?a.slice(0,-3)+"Hu\u2019":-1!==e.indexOf("jar")?a.slice(0,-3)+"wen":-1!==e.indexOf("DIS")?a.slice(0,-3)+"ben":a+" ret"},s:"puS lup",ss:jn,m:"wa\u2019 tup",mm:jn,h:"wa\u2019 rep",hh:jn,d:"wa\u2019 jaj",dd:jn,M:"wa\u2019 jar",MM:jn,y:"wa\u2019 DIS",yy:jn},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}});var xn={1:"'inci",5:"'inci",8:"'inci",70:"'inci",80:"'inci",2:"'nci",7:"'nci",20:"'nci",50:"'nci",3:"'\xfcnc\xfc",4:"'\xfcnc\xfc",100:"'\xfcnc\xfc",6:"'nc\u0131",9:"'uncu",10:"'uncu",30:"'uncu",60:"'\u0131nc\u0131",90:"'\u0131nc\u0131"};function Pn(e,a,t,s){e={s:["viensas secunds","'iensas secunds"],ss:[e+" secunds",e+" secunds"],m:["'n m\xedut","'iens m\xedut"],mm:[e+" m\xeduts",e+" m\xeduts"],h:["'n \xfeora","'iensa \xfeora"],hh:[e+" \xfeoras",e+" \xfeoras"],d:["'n ziua","'iensa ziua"],dd:[e+" ziuas",e+" ziuas"],M:["'n mes","'iens mes"],MM:[e+" mesen",e+" mesen"],y:["'n ar","'iens ar"],yy:[e+" ars",e+" ars"]};return s||a?e[t][0]:e[t][1]}function On(e,a,t){return"m"===t?a?"\u0445\u0432\u0438\u043b\u0438\u043d\u0430":"\u0445\u0432\u0438\u043b\u0438\u043d\u0443":"h"===t?a?"\u0433\u043e\u0434\u0438\u043d\u0430":"\u0433\u043e\u0434\u0438\u043d\u0443":e+" "+(e=+e,a=(a={ss:a?"\u0441\u0435\u043a\u0443\u043d\u0434\u0430_\u0441\u0435\u043a\u0443\u043d\u0434\u0438_\u0441\u0435\u043a\u0443\u043d\u0434":"\u0441\u0435\u043a\u0443\u043d\u0434\u0443_\u0441\u0435\u043a\u0443\u043d\u0434\u0438_\u0441\u0435\u043a\u0443\u043d\u0434",mm:a?"\u0445\u0432\u0438\u043b\u0438\u043d\u0430_\u0445\u0432\u0438\u043b\u0438\u043d\u0438_\u0445\u0432\u0438\u043b\u0438\u043d":"\u0445\u0432\u0438\u043b\u0438\u043d\u0443_\u0445\u0432\u0438\u043b\u0438\u043d\u0438_\u0445\u0432\u0438\u043b\u0438\u043d",hh:a?"\u0433\u043e\u0434\u0438\u043d\u0430_\u0433\u043e\u0434\u0438\u043d\u0438_\u0433\u043e\u0434\u0438\u043d":"\u0433\u043e\u0434\u0438\u043d\u0443_\u0433\u043e\u0434\u0438\u043d\u0438_\u0433\u043e\u0434\u0438\u043d",dd:"\u0434\u0435\u043d\u044c_\u0434\u043d\u0456_\u0434\u043d\u0456\u0432",MM:"\u043c\u0456\u0441\u044f\u0446\u044c_\u043c\u0456\u0441\u044f\u0446\u0456_\u043c\u0456\u0441\u044f\u0446\u0456\u0432",yy:"\u0440\u0456\u043a_\u0440\u043e\u043a\u0438_\u0440\u043e\u043a\u0456\u0432"}[t]).split("_"),e%10==1&&e%100!=11?a[0]:2<=e%10&&e%10<=4&&(e%100<10||20<=e%100)?a[1]:a[2])}function Wn(e){return function(){return e+"\u043e"+(11===this.hours()?"\u0431":"")+"] LT"}}M.defineLocale("tr",{months:"Ocak_\u015eubat_Mart_Nisan_May\u0131s_Haziran_Temmuz_A\u011fustos_Eyl\xfcl_Ekim_Kas\u0131m_Aral\u0131k".split("_"),monthsShort:"Oca_\u015eub_Mar_Nis_May_Haz_Tem_A\u011fu_Eyl_Eki_Kas_Ara".split("_"),weekdays:"Pazar_Pazartesi_Sal\u0131_\xc7ar\u015famba_Per\u015fembe_Cuma_Cumartesi".split("_"),weekdaysShort:"Paz_Pzt_Sal_\xc7ar_Per_Cum_Cmt".split("_"),weekdaysMin:"Pz_Pt_Sa_\xc7a_Pe_Cu_Ct".split("_"),meridiem:function(e,a,t){return e<12?t?"\xf6\xf6":"\xd6\xd6":t?"\xf6s":"\xd6S"},meridiemParse:/\xf6\xf6|\xd6\xd6|\xf6s|\xd6S/,isPM:function(e){return"\xf6s"===e||"\xd6S"===e},longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[bug\xfcn saat] LT",nextDay:"[yar\u0131n saat] LT",nextWeek:"[gelecek] dddd [saat] LT",lastDay:"[d\xfcn] LT",lastWeek:"[ge\xe7en] dddd [saat] LT",sameElse:"L"},relativeTime:{future:"%s sonra",past:"%s \xf6nce",s:"birka\xe7 saniye",ss:"%d saniye",m:"bir dakika",mm:"%d dakika",h:"bir saat",hh:"%d saat",d:"bir g\xfcn",dd:"%d g\xfcn",w:"bir hafta",ww:"%d hafta",M:"bir ay",MM:"%d ay",y:"bir y\u0131l",yy:"%d y\u0131l"},ordinal:function(e,a){switch(a){case"d":case"D":case"Do":case"DD":return e;default:if(0===e)return e+"'\u0131nc\u0131";var t=e%10;return e+(xn[t]||xn[e%100-t]||xn[100<=e?100:null])}},week:{dow:1,doy:7}}),M.defineLocale("tzl",{months:"Januar_Fevraglh_Mar\xe7_Avr\xefu_Mai_G\xfcn_Julia_Guscht_Setemvar_Listop\xe4ts_Noemvar_Zecemvar".split("_"),monthsShort:"Jan_Fev_Mar_Avr_Mai_G\xfcn_Jul_Gus_Set_Lis_Noe_Zec".split("_"),weekdays:"S\xfaladi_L\xfane\xe7i_Maitzi_M\xe1rcuri_Xh\xfaadi_Vi\xe9ner\xe7i_S\xe1turi".split("_"),weekdaysShort:"S\xfal_L\xfan_Mai_M\xe1r_Xh\xfa_Vi\xe9_S\xe1t".split("_"),weekdaysMin:"S\xfa_L\xfa_Ma_M\xe1_Xh_Vi_S\xe1".split("_"),longDateFormat:{LT:"HH.mm",LTS:"HH.mm.ss",L:"DD.MM.YYYY",LL:"D. MMMM [dallas] YYYY",LLL:"D. MMMM [dallas] YYYY HH.mm",LLLL:"dddd, [li] D. MMMM [dallas] YYYY HH.mm"},meridiemParse:/d\'o|d\'a/i,isPM:function(e){return"d'o"===e.toLowerCase()},meridiem:function(e,a,t){return 11<e?t?"d'o":"D'O":t?"d'a":"D'A"},calendar:{sameDay:"[oxhi \xe0] LT",nextDay:"[dem\xe0 \xe0] LT",nextWeek:"dddd [\xe0] LT",lastDay:"[ieiri \xe0] LT",lastWeek:"[s\xfcr el] dddd [lasteu \xe0] LT",sameElse:"L"},relativeTime:{future:"osprei %s",past:"ja%s",s:Pn,ss:Pn,m:Pn,mm:Pn,h:Pn,hh:Pn,d:Pn,dd:Pn,M:Pn,MM:Pn,y:Pn,yy:Pn},dayOfMonthOrdinalParse:/\d{1,2}\./,ordinal:"%d.",week:{dow:1,doy:4}}),M.defineLocale("tzm-latn",{months:"innayr_br\u02e4ayr\u02e4_mar\u02e4s\u02e4_ibrir_mayyw_ywnyw_ywlywz_\u0263w\u0161t_\u0161wtanbir_kt\u02e4wbr\u02e4_nwwanbir_dwjnbir".split("_"),monthsShort:"innayr_br\u02e4ayr\u02e4_mar\u02e4s\u02e4_ibrir_mayyw_ywnyw_ywlywz_\u0263w\u0161t_\u0161wtanbir_kt\u02e4wbr\u02e4_nwwanbir_dwjnbir".split("_"),weekdays:"asamas_aynas_asinas_akras_akwas_asimwas_asi\u1e0dyas".split("_"),weekdaysShort:"asamas_aynas_asinas_akras_akwas_asimwas_asi\u1e0dyas".split("_"),weekdaysMin:"asamas_aynas_asinas_akras_akwas_asimwas_asi\u1e0dyas".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[asdkh g] LT",nextDay:"[aska g] LT",nextWeek:"dddd [g] LT",lastDay:"[assant g] LT",lastWeek:"dddd [g] LT",sameElse:"L"},relativeTime:{future:"dadkh s yan %s",past:"yan %s",s:"imik",ss:"%d imik",m:"minu\u1e0d",mm:"%d minu\u1e0d",h:"sa\u025ba",hh:"%d tassa\u025bin",d:"ass",dd:"%d ossan",M:"ayowr",MM:"%d iyyirn",y:"asgas",yy:"%d isgasn"},week:{dow:6,doy:12}}),M.defineLocale("tzm",{months:"\u2d49\u2d4f\u2d4f\u2d30\u2d62\u2d54_\u2d31\u2d55\u2d30\u2d62\u2d55_\u2d4e\u2d30\u2d55\u2d5a_\u2d49\u2d31\u2d54\u2d49\u2d54_\u2d4e\u2d30\u2d62\u2d62\u2d53_\u2d62\u2d53\u2d4f\u2d62\u2d53_\u2d62\u2d53\u2d4d\u2d62\u2d53\u2d63_\u2d56\u2d53\u2d5b\u2d5c_\u2d5b\u2d53\u2d5c\u2d30\u2d4f\u2d31\u2d49\u2d54_\u2d3d\u2d5f\u2d53\u2d31\u2d55_\u2d4f\u2d53\u2d61\u2d30\u2d4f\u2d31\u2d49\u2d54_\u2d37\u2d53\u2d4a\u2d4f\u2d31\u2d49\u2d54".split("_"),monthsShort:"\u2d49\u2d4f\u2d4f\u2d30\u2d62\u2d54_\u2d31\u2d55\u2d30\u2d62\u2d55_\u2d4e\u2d30\u2d55\u2d5a_\u2d49\u2d31\u2d54\u2d49\u2d54_\u2d4e\u2d30\u2d62\u2d62\u2d53_\u2d62\u2d53\u2d4f\u2d62\u2d53_\u2d62\u2d53\u2d4d\u2d62\u2d53\u2d63_\u2d56\u2d53\u2d5b\u2d5c_\u2d5b\u2d53\u2d5c\u2d30\u2d4f\u2d31\u2d49\u2d54_\u2d3d\u2d5f\u2d53\u2d31\u2d55_\u2d4f\u2d53\u2d61\u2d30\u2d4f\u2d31\u2d49\u2d54_\u2d37\u2d53\u2d4a\u2d4f\u2d31\u2d49\u2d54".split("_"),weekdays:"\u2d30\u2d59\u2d30\u2d4e\u2d30\u2d59_\u2d30\u2d62\u2d4f\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4f\u2d30\u2d59_\u2d30\u2d3d\u2d54\u2d30\u2d59_\u2d30\u2d3d\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4e\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d39\u2d62\u2d30\u2d59".split("_"),weekdaysShort:"\u2d30\u2d59\u2d30\u2d4e\u2d30\u2d59_\u2d30\u2d62\u2d4f\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4f\u2d30\u2d59_\u2d30\u2d3d\u2d54\u2d30\u2d59_\u2d30\u2d3d\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4e\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d39\u2d62\u2d30\u2d59".split("_"),weekdaysMin:"\u2d30\u2d59\u2d30\u2d4e\u2d30\u2d59_\u2d30\u2d62\u2d4f\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4f\u2d30\u2d59_\u2d30\u2d3d\u2d54\u2d30\u2d59_\u2d30\u2d3d\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d4e\u2d61\u2d30\u2d59_\u2d30\u2d59\u2d49\u2d39\u2d62\u2d30\u2d59".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd D MMMM YYYY HH:mm"},calendar:{sameDay:"[\u2d30\u2d59\u2d37\u2d45 \u2d34] LT",nextDay:"[\u2d30\u2d59\u2d3d\u2d30 \u2d34] LT",nextWeek:"dddd [\u2d34] LT",lastDay:"[\u2d30\u2d5a\u2d30\u2d4f\u2d5c \u2d34] LT",lastWeek:"dddd [\u2d34] LT",sameElse:"L"},relativeTime:{future:"\u2d37\u2d30\u2d37\u2d45 \u2d59 \u2d62\u2d30\u2d4f %s",past:"\u2d62\u2d30\u2d4f %s",s:"\u2d49\u2d4e\u2d49\u2d3d",ss:"%d \u2d49\u2d4e\u2d49\u2d3d",m:"\u2d4e\u2d49\u2d4f\u2d53\u2d3a",mm:"%d \u2d4e\u2d49\u2d4f\u2d53\u2d3a",h:"\u2d59\u2d30\u2d44\u2d30",hh:"%d \u2d5c\u2d30\u2d59\u2d59\u2d30\u2d44\u2d49\u2d4f",d:"\u2d30\u2d59\u2d59",dd:"%d o\u2d59\u2d59\u2d30\u2d4f",M:"\u2d30\u2d62o\u2d53\u2d54",MM:"%d \u2d49\u2d62\u2d62\u2d49\u2d54\u2d4f",y:"\u2d30\u2d59\u2d33\u2d30\u2d59",yy:"%d \u2d49\u2d59\u2d33\u2d30\u2d59\u2d4f"},week:{dow:6,doy:12}}),M.defineLocale("ug-cn",{months:"\u064a\u0627\u0646\u06cb\u0627\u0631_\u0641\u06d0\u06cb\u0631\u0627\u0644_\u0645\u0627\u0631\u062a_\u0626\u0627\u067e\u0631\u06d0\u0644_\u0645\u0627\u064a_\u0626\u0649\u064a\u06c7\u0646_\u0626\u0649\u064a\u06c7\u0644_\u0626\u0627\u06cb\u063a\u06c7\u0633\u062a_\u0633\u06d0\u0646\u062a\u06d5\u0628\u0649\u0631_\u0626\u06c6\u0643\u062a\u06d5\u0628\u0649\u0631_\u0646\u0648\u064a\u0627\u0628\u0649\u0631_\u062f\u06d0\u0643\u0627\u0628\u0649\u0631".split("_"),monthsShort:"\u064a\u0627\u0646\u06cb\u0627\u0631_\u0641\u06d0\u06cb\u0631\u0627\u0644_\u0645\u0627\u0631\u062a_\u0626\u0627\u067e\u0631\u06d0\u0644_\u0645\u0627\u064a_\u0626\u0649\u064a\u06c7\u0646_\u0626\u0649\u064a\u06c7\u0644_\u0626\u0627\u06cb\u063a\u06c7\u0633\u062a_\u0633\u06d0\u0646\u062a\u06d5\u0628\u0649\u0631_\u0626\u06c6\u0643\u062a\u06d5\u0628\u0649\u0631_\u0646\u0648\u064a\u0627\u0628\u0649\u0631_\u062f\u06d0\u0643\u0627\u0628\u0649\u0631".split("_"),weekdays:"\u064a\u06d5\u0643\u0634\u06d5\u0646\u0628\u06d5_\u062f\u06c8\u0634\u06d5\u0646\u0628\u06d5_\u0633\u06d5\u064a\u0634\u06d5\u0646\u0628\u06d5_\u0686\u0627\u0631\u0634\u06d5\u0646\u0628\u06d5_\u067e\u06d5\u064a\u0634\u06d5\u0646\u0628\u06d5_\u062c\u06c8\u0645\u06d5_\u0634\u06d5\u0646\u0628\u06d5".split("_"),weekdaysShort:"\u064a\u06d5_\u062f\u06c8_\u0633\u06d5_\u0686\u0627_\u067e\u06d5_\u062c\u06c8_\u0634\u06d5".split("_"),weekdaysMin:"\u064a\u06d5_\u062f\u06c8_\u0633\u06d5_\u0686\u0627_\u067e\u06d5_\u062c\u06c8_\u0634\u06d5".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY-MM-DD",LL:"YYYY-\u064a\u0649\u0644\u0649M-\u0626\u0627\u064a\u0646\u0649\u06adD-\u0643\u06c8\u0646\u0649",LLL:"YYYY-\u064a\u0649\u0644\u0649M-\u0626\u0627\u064a\u0646\u0649\u06adD-\u0643\u06c8\u0646\u0649\u060c HH:mm",LLLL:"dddd\u060c YYYY-\u064a\u0649\u0644\u0649M-\u0626\u0627\u064a\u0646\u0649\u06adD-\u0643\u06c8\u0646\u0649\u060c HH:mm"},meridiemParse:/\u064a\u06d0\u0631\u0649\u0645 \u0643\u06d0\u0686\u06d5|\u0633\u06d5\u06be\u06d5\u0631|\u0686\u06c8\u0634\u062a\u0649\u0646 \u0628\u06c7\u0631\u06c7\u0646|\u0686\u06c8\u0634|\u0686\u06c8\u0634\u062a\u0649\u0646 \u0643\u06d0\u064a\u0649\u0646|\u0643\u06d5\u0686/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u064a\u06d0\u0631\u0649\u0645 \u0643\u06d0\u0686\u06d5"===a||"\u0633\u06d5\u06be\u06d5\u0631"===a||"\u0686\u06c8\u0634\u062a\u0649\u0646 \u0628\u06c7\u0631\u06c7\u0646"===a||"\u0686\u06c8\u0634\u062a\u0649\u0646 \u0643\u06d0\u064a\u0649\u0646"!==a&&"\u0643\u06d5\u0686"!==a&&11<=e?e:e+12},meridiem:function(e,a,t){e=100*e+a;return e<600?"\u064a\u06d0\u0631\u0649\u0645 \u0643\u06d0\u0686\u06d5":e<900?"\u0633\u06d5\u06be\u06d5\u0631":e<1130?"\u0686\u06c8\u0634\u062a\u0649\u0646 \u0628\u06c7\u0631\u06c7\u0646":e<1230?"\u0686\u06c8\u0634":e<1800?"\u0686\u06c8\u0634\u062a\u0649\u0646 \u0643\u06d0\u064a\u0649\u0646":"\u0643\u06d5\u0686"},calendar:{sameDay:"[\u0628\u06c8\u06af\u06c8\u0646 \u0633\u0627\u0626\u06d5\u062a] LT",nextDay:"[\u0626\u06d5\u062a\u06d5 \u0633\u0627\u0626\u06d5\u062a] LT",nextWeek:"[\u0643\u06d0\u0644\u06d5\u0631\u0643\u0649] dddd [\u0633\u0627\u0626\u06d5\u062a] LT",lastDay:"[\u062a\u06c6\u0646\u06c8\u06af\u06c8\u0646] LT",lastWeek:"[\u0626\u0627\u0644\u062f\u0649\u0646\u0642\u0649] dddd [\u0633\u0627\u0626\u06d5\u062a] LT",sameElse:"L"},relativeTime:{future:"%s \u0643\u06d0\u064a\u0649\u0646",past:"%s \u0628\u06c7\u0631\u06c7\u0646",s:"\u0646\u06d5\u0686\u0686\u06d5 \u0633\u06d0\u0643\u0648\u0646\u062a",ss:"%d \u0633\u06d0\u0643\u0648\u0646\u062a",m:"\u0628\u0649\u0631 \u0645\u0649\u0646\u06c7\u062a",mm:"%d \u0645\u0649\u0646\u06c7\u062a",h:"\u0628\u0649\u0631 \u0633\u0627\u0626\u06d5\u062a",hh:"%d \u0633\u0627\u0626\u06d5\u062a",d:"\u0628\u0649\u0631 \u0643\u06c8\u0646",dd:"%d \u0643\u06c8\u0646",M:"\u0628\u0649\u0631 \u0626\u0627\u064a",MM:"%d \u0626\u0627\u064a",y:"\u0628\u0649\u0631 \u064a\u0649\u0644",yy:"%d \u064a\u0649\u0644"},dayOfMonthOrdinalParse:/\d{1,2}(-\u0643\u06c8\u0646\u0649|-\u0626\u0627\u064a|-\u06be\u06d5\u067e\u062a\u06d5)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"-\u0643\u06c8\u0646\u0649";case"w":case"W":return e+"-\u06be\u06d5\u067e\u062a\u06d5";default:return e}},preparse:function(e){return e.replace(/\u060c/g,",")},postformat:function(e){return e.replace(/,/g,"\u060c")},week:{dow:1,doy:7}}),M.defineLocale("uk",{months:{format:"\u0441\u0456\u0447\u043d\u044f_\u043b\u044e\u0442\u043e\u0433\u043e_\u0431\u0435\u0440\u0435\u0437\u043d\u044f_\u043a\u0432\u0456\u0442\u043d\u044f_\u0442\u0440\u0430\u0432\u043d\u044f_\u0447\u0435\u0440\u0432\u043d\u044f_\u043b\u0438\u043f\u043d\u044f_\u0441\u0435\u0440\u043f\u043d\u044f_\u0432\u0435\u0440\u0435\u0441\u043d\u044f_\u0436\u043e\u0432\u0442\u043d\u044f_\u043b\u0438\u0441\u0442\u043e\u043f\u0430\u0434\u0430_\u0433\u0440\u0443\u0434\u043d\u044f".split("_"),standalone:"\u0441\u0456\u0447\u0435\u043d\u044c_\u043b\u044e\u0442\u0438\u0439_\u0431\u0435\u0440\u0435\u0437\u0435\u043d\u044c_\u043a\u0432\u0456\u0442\u0435\u043d\u044c_\u0442\u0440\u0430\u0432\u0435\u043d\u044c_\u0447\u0435\u0440\u0432\u0435\u043d\u044c_\u043b\u0438\u043f\u0435\u043d\u044c_\u0441\u0435\u0440\u043f\u0435\u043d\u044c_\u0432\u0435\u0440\u0435\u0441\u0435\u043d\u044c_\u0436\u043e\u0432\u0442\u0435\u043d\u044c_\u043b\u0438\u0441\u0442\u043e\u043f\u0430\u0434_\u0433\u0440\u0443\u0434\u0435\u043d\u044c".split("_")},monthsShort:"\u0441\u0456\u0447_\u043b\u044e\u0442_\u0431\u0435\u0440_\u043a\u0432\u0456\u0442_\u0442\u0440\u0430\u0432_\u0447\u0435\u0440\u0432_\u043b\u0438\u043f_\u0441\u0435\u0440\u043f_\u0432\u0435\u0440_\u0436\u043e\u0432\u0442_\u043b\u0438\u0441\u0442_\u0433\u0440\u0443\u0434".split("_"),weekdays:function(e,a){var t={nominative:"\u043d\u0435\u0434\u0456\u043b\u044f_\u043f\u043e\u043d\u0435\u0434\u0456\u043b\u043e\u043a_\u0432\u0456\u0432\u0442\u043e\u0440\u043e\u043a_\u0441\u0435\u0440\u0435\u0434\u0430_\u0447\u0435\u0442\u0432\u0435\u0440_\u043f\u2019\u044f\u0442\u043d\u0438\u0446\u044f_\u0441\u0443\u0431\u043e\u0442\u0430".split("_"),accusative:"\u043d\u0435\u0434\u0456\u043b\u044e_\u043f\u043e\u043d\u0435\u0434\u0456\u043b\u043e\u043a_\u0432\u0456\u0432\u0442\u043e\u0440\u043e\u043a_\u0441\u0435\u0440\u0435\u0434\u0443_\u0447\u0435\u0442\u0432\u0435\u0440_\u043f\u2019\u044f\u0442\u043d\u0438\u0446\u044e_\u0441\u0443\u0431\u043e\u0442\u0443".split("_"),genitive:"\u043d\u0435\u0434\u0456\u043b\u0456_\u043f\u043e\u043d\u0435\u0434\u0456\u043b\u043a\u0430_\u0432\u0456\u0432\u0442\u043e\u0440\u043a\u0430_\u0441\u0435\u0440\u0435\u0434\u0438_\u0447\u0435\u0442\u0432\u0435\u0440\u0433\u0430_\u043f\u2019\u044f\u0442\u043d\u0438\u0446\u0456_\u0441\u0443\u0431\u043e\u0442\u0438".split("_")};return!0===e?t.nominative.slice(1,7).concat(t.nominative.slice(0,1)):e?t[/(\[[\u0412\u0432\u0423\u0443]\]) ?dddd/.test(a)?"accusative":/\[?(?:\u043c\u0438\u043d\u0443\u043b\u043e\u0457|\u043d\u0430\u0441\u0442\u0443\u043f\u043d\u043e\u0457)? ?\] ?dddd/.test(a)?"genitive":"nominative"][e.day()]:t.nominative},weekdaysShort:"\u043d\u0434_\u043f\u043d_\u0432\u0442_\u0441\u0440_\u0447\u0442_\u043f\u0442_\u0441\u0431".split("_"),weekdaysMin:"\u043d\u0434_\u043f\u043d_\u0432\u0442_\u0441\u0440_\u0447\u0442_\u043f\u0442_\u0441\u0431".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD.MM.YYYY",LL:"D MMMM YYYY \u0440.",LLL:"D MMMM YYYY \u0440., HH:mm",LLLL:"dddd, D MMMM YYYY \u0440., HH:mm"},calendar:{sameDay:Wn("[\u0421\u044c\u043e\u0433\u043e\u0434\u043d\u0456 "),nextDay:Wn("[\u0417\u0430\u0432\u0442\u0440\u0430 "),lastDay:Wn("[\u0412\u0447\u043e\u0440\u0430 "),nextWeek:Wn("[\u0423] dddd ["),lastWeek:function(){switch(this.day()){case 0:case 3:case 5:case 6:return Wn("[\u041c\u0438\u043d\u0443\u043b\u043e\u0457] dddd [").call(this);case 1:case 2:case 4:return Wn("[\u041c\u0438\u043d\u0443\u043b\u043e\u0433\u043e] dddd [").call(this)}},sameElse:"L"},relativeTime:{future:"\u0437\u0430 %s",past:"%s \u0442\u043e\u043c\u0443",s:"\u0434\u0435\u043a\u0456\u043b\u044c\u043a\u0430 \u0441\u0435\u043a\u0443\u043d\u0434",ss:On,m:On,mm:On,h:"\u0433\u043e\u0434\u0438\u043d\u0443",hh:On,d:"\u0434\u0435\u043d\u044c",dd:On,M:"\u043c\u0456\u0441\u044f\u0446\u044c",MM:On,y:"\u0440\u0456\u043a",yy:On},meridiemParse:/\u043d\u043e\u0447\u0456|\u0440\u0430\u043d\u043a\u0443|\u0434\u043d\u044f|\u0432\u0435\u0447\u043e\u0440\u0430/,isPM:function(e){return/^(\u0434\u043d\u044f|\u0432\u0435\u0447\u043e\u0440\u0430)$/.test(e)},meridiem:function(e,a,t){return e<4?"\u043d\u043e\u0447\u0456":e<12?"\u0440\u0430\u043d\u043a\u0443":e<17?"\u0434\u043d\u044f":"\u0432\u0435\u0447\u043e\u0440\u0430"},dayOfMonthOrdinalParse:/\d{1,2}-(\u0439|\u0433\u043e)/,ordinal:function(e,a){switch(a){case"M":case"d":case"DDD":case"w":case"W":return e+"-\u0439";case"D":return e+"-\u0433\u043e";default:return e}},week:{dow:1,doy:7}});jt=["\u062c\u0646\u0648\u0631\u06cc","\u0641\u0631\u0648\u0631\u06cc","\u0645\u0627\u0631\u0686","\u0627\u067e\u0631\u06cc\u0644","\u0645\u0626\u06cc","\u062c\u0648\u0646","\u062c\u0648\u0644\u0627\u0626\u06cc","\u0627\u06af\u0633\u062a","\u0633\u062a\u0645\u0628\u0631","\u0627\u06a9\u062a\u0648\u0628\u0631","\u0646\u0648\u0645\u0628\u0631","\u062f\u0633\u0645\u0628\u0631"],vt=["\u0627\u062a\u0648\u0627\u0631","\u067e\u06cc\u0631","\u0645\u0646\u06af\u0644","\u0628\u062f\u06be","\u062c\u0645\u0639\u0631\u0627\u062a","\u062c\u0645\u0639\u06c1","\u06c1\u0641\u062a\u06c1"];return M.defineLocale("ur",{months:jt,monthsShort:jt,weekdays:vt,weekdaysShort:vt,weekdaysMin:vt,longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd\u060c D MMMM YYYY HH:mm"},meridiemParse:/\u0635\u0628\u062d|\u0634\u0627\u0645/,isPM:function(e){return"\u0634\u0627\u0645"===e},meridiem:function(e,a,t){return e<12?"\u0635\u0628\u062d":"\u0634\u0627\u0645"},calendar:{sameDay:"[\u0622\u062c \u0628\u0648\u0642\u062a] LT",nextDay:"[\u06a9\u0644 \u0628\u0648\u0642\u062a] LT",nextWeek:"dddd [\u0628\u0648\u0642\u062a] LT",lastDay:"[\u06af\u0630\u0634\u062a\u06c1 \u0631\u0648\u0632 \u0628\u0648\u0642\u062a] LT",lastWeek:"[\u06af\u0630\u0634\u062a\u06c1] dddd [\u0628\u0648\u0642\u062a] LT",sameElse:"L"},relativeTime:{future:"%s \u0628\u0639\u062f",past:"%s \u0642\u0628\u0644",s:"\u0686\u0646\u062f \u0633\u06cc\u06a9\u0646\u0688",ss:"%d \u0633\u06cc\u06a9\u0646\u0688",m:"\u0627\u06cc\u06a9 \u0645\u0646\u0679",mm:"%d \u0645\u0646\u0679",h:"\u0627\u06cc\u06a9 \u06af\u06be\u0646\u0679\u06c1",hh:"%d \u06af\u06be\u0646\u0679\u06d2",d:"\u0627\u06cc\u06a9 \u062f\u0646",dd:"%d \u062f\u0646",M:"\u0627\u06cc\u06a9 \u0645\u0627\u06c1",MM:"%d \u0645\u0627\u06c1",y:"\u0627\u06cc\u06a9 \u0633\u0627\u0644",yy:"%d \u0633\u0627\u0644"},preparse:function(e){return e.replace(/\u060c/g,",")},postformat:function(e){return e.replace(/,/g,"\u060c")},week:{dow:1,doy:4}}),M.defineLocale("uz-latn",{months:"Yanvar_Fevral_Mart_Aprel_May_Iyun_Iyul_Avgust_Sentabr_Oktabr_Noyabr_Dekabr".split("_"),monthsShort:"Yan_Fev_Mar_Apr_May_Iyun_Iyul_Avg_Sen_Okt_Noy_Dek".split("_"),weekdays:"Yakshanba_Dushanba_Seshanba_Chorshanba_Payshanba_Juma_Shanba".split("_"),weekdaysShort:"Yak_Dush_Sesh_Chor_Pay_Jum_Shan".split("_"),weekdaysMin:"Ya_Du_Se_Cho_Pa_Ju_Sha".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"D MMMM YYYY, dddd HH:mm"},calendar:{sameDay:"[Bugun soat] LT [da]",nextDay:"[Ertaga] LT [da]",nextWeek:"dddd [kuni soat] LT [da]",lastDay:"[Kecha soat] LT [da]",lastWeek:"[O'tgan] dddd [kuni soat] LT [da]",sameElse:"L"},relativeTime:{future:"Yaqin %s ichida",past:"Bir necha %s oldin",s:"soniya",ss:"%d soniya",m:"bir daqiqa",mm:"%d daqiqa",h:"bir soat",hh:"%d soat",d:"bir kun",dd:"%d kun",M:"bir oy",MM:"%d oy",y:"bir yil",yy:"%d yil"},week:{dow:1,doy:7}}),M.defineLocale("uz",{months:"\u044f\u043d\u0432\u0430\u0440_\u0444\u0435\u0432\u0440\u0430\u043b_\u043c\u0430\u0440\u0442_\u0430\u043f\u0440\u0435\u043b_\u043c\u0430\u0439_\u0438\u044e\u043d_\u0438\u044e\u043b_\u0430\u0432\u0433\u0443\u0441\u0442_\u0441\u0435\u043d\u0442\u044f\u0431\u0440_\u043e\u043a\u0442\u044f\u0431\u0440_\u043d\u043e\u044f\u0431\u0440_\u0434\u0435\u043a\u0430\u0431\u0440".split("_"),monthsShort:"\u044f\u043d\u0432_\u0444\u0435\u0432_\u043c\u0430\u0440_\u0430\u043f\u0440_\u043c\u0430\u0439_\u0438\u044e\u043d_\u0438\u044e\u043b_\u0430\u0432\u0433_\u0441\u0435\u043d_\u043e\u043a\u0442_\u043d\u043e\u044f_\u0434\u0435\u043a".split("_"),weekdays:"\u042f\u043a\u0448\u0430\u043d\u0431\u0430_\u0414\u0443\u0448\u0430\u043d\u0431\u0430_\u0421\u0435\u0448\u0430\u043d\u0431\u0430_\u0427\u043e\u0440\u0448\u0430\u043d\u0431\u0430_\u041f\u0430\u0439\u0448\u0430\u043d\u0431\u0430_\u0416\u0443\u043c\u0430_\u0428\u0430\u043d\u0431\u0430".split("_"),weekdaysShort:"\u042f\u043a\u0448_\u0414\u0443\u0448_\u0421\u0435\u0448_\u0427\u043e\u0440_\u041f\u0430\u0439_\u0416\u0443\u043c_\u0428\u0430\u043d".split("_"),weekdaysMin:"\u042f\u043a_\u0414\u0443_\u0421\u0435_\u0427\u043e_\u041f\u0430_\u0416\u0443_\u0428\u0430".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"D MMMM YYYY, dddd HH:mm"},calendar:{sameDay:"[\u0411\u0443\u0433\u0443\u043d \u0441\u043e\u0430\u0442] LT [\u0434\u0430]",nextDay:"[\u042d\u0440\u0442\u0430\u0433\u0430] LT [\u0434\u0430]",nextWeek:"dddd [\u043a\u0443\u043d\u0438 \u0441\u043e\u0430\u0442] LT [\u0434\u0430]",lastDay:"[\u041a\u0435\u0447\u0430 \u0441\u043e\u0430\u0442] LT [\u0434\u0430]",lastWeek:"[\u0423\u0442\u0433\u0430\u043d] dddd [\u043a\u0443\u043d\u0438 \u0441\u043e\u0430\u0442] LT [\u0434\u0430]",sameElse:"L"},relativeTime:{future:"\u042f\u043a\u0438\u043d %s \u0438\u0447\u0438\u0434\u0430",past:"\u0411\u0438\u0440 \u043d\u0435\u0447\u0430 %s \u043e\u043b\u0434\u0438\u043d",s:"\u0444\u0443\u0440\u0441\u0430\u0442",ss:"%d \u0444\u0443\u0440\u0441\u0430\u0442",m:"\u0431\u0438\u0440 \u0434\u0430\u043a\u0438\u043a\u0430",mm:"%d \u0434\u0430\u043a\u0438\u043a\u0430",h:"\u0431\u0438\u0440 \u0441\u043e\u0430\u0442",hh:"%d \u0441\u043e\u0430\u0442",d:"\u0431\u0438\u0440 \u043a\u0443\u043d",dd:"%d \u043a\u0443\u043d",M:"\u0431\u0438\u0440 \u043e\u0439",MM:"%d \u043e\u0439",y:"\u0431\u0438\u0440 \u0439\u0438\u043b",yy:"%d \u0439\u0438\u043b"},week:{dow:1,doy:7}}),M.defineLocale("vi",{months:"th\xe1ng 1_th\xe1ng 2_th\xe1ng 3_th\xe1ng 4_th\xe1ng 5_th\xe1ng 6_th\xe1ng 7_th\xe1ng 8_th\xe1ng 9_th\xe1ng 10_th\xe1ng 11_th\xe1ng 12".split("_"),monthsShort:"Thg 01_Thg 02_Thg 03_Thg 04_Thg 05_Thg 06_Thg 07_Thg 08_Thg 09_Thg 10_Thg 11_Thg 12".split("_"),monthsParseExact:!0,weekdays:"ch\u1ee7 nh\u1eadt_th\u1ee9 hai_th\u1ee9 ba_th\u1ee9 t\u01b0_th\u1ee9 n\u0103m_th\u1ee9 s\xe1u_th\u1ee9 b\u1ea3y".split("_"),weekdaysShort:"CN_T2_T3_T4_T5_T6_T7".split("_"),weekdaysMin:"CN_T2_T3_T4_T5_T6_T7".split("_"),weekdaysParseExact:!0,meridiemParse:/sa|ch/i,isPM:function(e){return/^ch$/i.test(e)},meridiem:function(e,a,t){return e<12?t?"sa":"SA":t?"ch":"CH"},longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"D MMMM [n\u0103m] YYYY",LLL:"D MMMM [n\u0103m] YYYY HH:mm",LLLL:"dddd, D MMMM [n\u0103m] YYYY HH:mm",l:"DD/M/YYYY",ll:"D MMM YYYY",lll:"D MMM YYYY HH:mm",llll:"ddd, D MMM YYYY HH:mm"},calendar:{sameDay:"[H\xf4m nay l\xfac] LT",nextDay:"[Ng\xe0y mai l\xfac] LT",nextWeek:"dddd [tu\u1ea7n t\u1edbi l\xfac] LT",lastDay:"[H\xf4m qua l\xfac] LT",lastWeek:"dddd [tu\u1ea7n tr\u01b0\u1edbc l\xfac] LT",sameElse:"L"},relativeTime:{future:"%s t\u1edbi",past:"%s tr\u01b0\u1edbc",s:"v\xe0i gi\xe2y",ss:"%d gi\xe2y",m:"m\u1ed9t ph\xfat",mm:"%d ph\xfat",h:"m\u1ed9t gi\u1edd",hh:"%d gi\u1edd",d:"m\u1ed9t ng\xe0y",dd:"%d ng\xe0y",w:"m\u1ed9t tu\u1ea7n",ww:"%d tu\u1ea7n",M:"m\u1ed9t th\xe1ng",MM:"%d th\xe1ng",y:"m\u1ed9t n\u0103m",yy:"%d n\u0103m"},dayOfMonthOrdinalParse:/\d{1,2}/,ordinal:function(e){return e},week:{dow:1,doy:4}}),M.defineLocale("x-pseudo",{months:"J~\xe1\xf1\xfa\xe1~r\xfd_F~\xe9br\xfa~\xe1r\xfd_~M\xe1rc~h_\xc1p~r\xedl_~M\xe1\xfd_~J\xfa\xf1\xe9~_J\xfal~\xfd_\xc1\xfa~g\xfast~_S\xe9p~t\xe9mb~\xe9r_\xd3~ct\xf3b~\xe9r_\xd1~\xf3v\xe9m~b\xe9r_~D\xe9c\xe9~mb\xe9r".split("_"),monthsShort:"J~\xe1\xf1_~F\xe9b_~M\xe1r_~\xc1pr_~M\xe1\xfd_~J\xfa\xf1_~J\xfal_~\xc1\xfag_~S\xe9p_~\xd3ct_~\xd1\xf3v_~D\xe9c".split("_"),monthsParseExact:!0,weekdays:"S~\xfa\xf1d\xe1~\xfd_M\xf3~\xf1d\xe1\xfd~_T\xfa\xe9~sd\xe1\xfd~_W\xe9d~\xf1\xe9sd~\xe1\xfd_T~h\xfars~d\xe1\xfd_~Fr\xedd~\xe1\xfd_S~\xe1t\xfar~d\xe1\xfd".split("_"),weekdaysShort:"S~\xfa\xf1_~M\xf3\xf1_~T\xfa\xe9_~W\xe9d_~Th\xfa_~Fr\xed_~S\xe1t".split("_"),weekdaysMin:"S~\xfa_M\xf3~_T\xfa_~W\xe9_T~h_Fr~_S\xe1".split("_"),weekdaysParseExact:!0,longDateFormat:{LT:"HH:mm",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY HH:mm",LLLL:"dddd, D MMMM YYYY HH:mm"},calendar:{sameDay:"[T~\xf3d\xe1~\xfd \xe1t] LT",nextDay:"[T~\xf3m\xf3~rr\xf3~w \xe1t] LT",nextWeek:"dddd [\xe1t] LT",lastDay:"[\xdd~\xe9st~\xe9rd\xe1~\xfd \xe1t] LT",lastWeek:"[L~\xe1st] dddd [\xe1t] LT",sameElse:"L"},relativeTime:{future:"\xed~\xf1 %s",past:"%s \xe1~g\xf3",s:"\xe1 ~f\xe9w ~s\xe9c\xf3~\xf1ds",ss:"%d s~\xe9c\xf3\xf1~ds",m:"\xe1 ~m\xed\xf1~\xfat\xe9",mm:"%d m~\xed\xf1\xfa~t\xe9s",h:"\xe1~\xf1 h\xf3~\xfar",hh:"%d h~\xf3\xfars",d:"\xe1 ~d\xe1\xfd",dd:"%d d~\xe1\xfds",M:"\xe1 ~m\xf3\xf1~th",MM:"%d m~\xf3\xf1t~hs",y:"\xe1 ~\xfd\xe9\xe1r",yy:"%d \xfd~\xe9\xe1rs"},dayOfMonthOrdinalParse:/\d{1,2}(th|st|nd|rd)/,ordinal:function(e){var a=e%10;return e+(1==~~(e%100/10)?"th":1==a?"st":2==a?"nd":3==a?"rd":"th")},week:{dow:1,doy:4}}),M.defineLocale("yo",{months:"S\u1eb9\u0301r\u1eb9\u0301_E\u0300re\u0300le\u0300_\u1eb8r\u1eb9\u0300na\u0300_I\u0300gbe\u0301_E\u0300bibi_O\u0300ku\u0300du_Ag\u1eb9mo_O\u0300gu\u0301n_Owewe_\u1ecc\u0300wa\u0300ra\u0300_Be\u0301lu\u0301_\u1ecc\u0300p\u1eb9\u0300\u0300".split("_"),monthsShort:"S\u1eb9\u0301r_E\u0300rl_\u1eb8rn_I\u0300gb_E\u0300bi_O\u0300ku\u0300_Ag\u1eb9_O\u0300gu\u0301_Owe_\u1ecc\u0300wa\u0300_Be\u0301l_\u1ecc\u0300p\u1eb9\u0300\u0300".split("_"),weekdays:"A\u0300i\u0300ku\u0301_Aje\u0301_I\u0300s\u1eb9\u0301gun_\u1eccj\u1ecd\u0301ru\u0301_\u1eccj\u1ecd\u0301b\u1ecd_\u1eb8ti\u0300_A\u0300ba\u0301m\u1eb9\u0301ta".split("_"),weekdaysShort:"A\u0300i\u0300k_Aje\u0301_I\u0300s\u1eb9\u0301_\u1eccjr_\u1eccjb_\u1eb8ti\u0300_A\u0300ba\u0301".split("_"),weekdaysMin:"A\u0300i\u0300_Aj_I\u0300s_\u1eccr_\u1eccb_\u1eb8t_A\u0300b".split("_"),longDateFormat:{LT:"h:mm A",LTS:"h:mm:ss A",L:"DD/MM/YYYY",LL:"D MMMM YYYY",LLL:"D MMMM YYYY h:mm A",LLLL:"dddd, D MMMM YYYY h:mm A"},calendar:{sameDay:"[O\u0300ni\u0300 ni] LT",nextDay:"[\u1ecc\u0300la ni] LT",nextWeek:"dddd [\u1eccs\u1eb9\u0300 to\u0301n'b\u1ecd] [ni] LT",lastDay:"[A\u0300na ni] LT",lastWeek:"dddd [\u1eccs\u1eb9\u0300 to\u0301l\u1ecd\u0301] [ni] LT",sameElse:"L"},relativeTime:{future:"ni\u0301 %s",past:"%s k\u1ecdja\u0301",s:"i\u0300s\u1eb9ju\u0301 aaya\u0301 die",ss:"aaya\u0301 %d",m:"i\u0300s\u1eb9ju\u0301 kan",mm:"i\u0300s\u1eb9ju\u0301 %d",h:"wa\u0301kati kan",hh:"wa\u0301kati %d",d:"\u1ecdj\u1ecd\u0301 kan",dd:"\u1ecdj\u1ecd\u0301 %d",M:"osu\u0300 kan",MM:"osu\u0300 %d",y:"\u1ecddu\u0301n kan",yy:"\u1ecddu\u0301n %d"},dayOfMonthOrdinalParse:/\u1ecdj\u1ecd\u0301\s\d{1,2}/,ordinal:"\u1ecdj\u1ecd\u0301 %d",week:{dow:1,doy:4}}),M.defineLocale("zh-cn",{months:"\u4e00\u6708_\u4e8c\u6708_\u4e09\u6708_\u56db\u6708_\u4e94\u6708_\u516d\u6708_\u4e03\u6708_\u516b\u6708_\u4e5d\u6708_\u5341\u6708_\u5341\u4e00\u6708_\u5341\u4e8c\u6708".split("_"),monthsShort:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),weekdays:"\u661f\u671f\u65e5_\u661f\u671f\u4e00_\u661f\u671f\u4e8c_\u661f\u671f\u4e09_\u661f\u671f\u56db_\u661f\u671f\u4e94_\u661f\u671f\u516d".split("_"),weekdaysShort:"\u5468\u65e5_\u5468\u4e00_\u5468\u4e8c_\u5468\u4e09_\u5468\u56db_\u5468\u4e94_\u5468\u516d".split("_"),weekdaysMin:"\u65e5_\u4e00_\u4e8c_\u4e09_\u56db_\u4e94_\u516d".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY/MM/DD",LL:"YYYY\u5e74M\u6708D\u65e5",LLL:"YYYY\u5e74M\u6708D\u65e5Ah\u70b9mm\u5206",LLLL:"YYYY\u5e74M\u6708D\u65e5ddddAh\u70b9mm\u5206",l:"YYYY/M/D",ll:"YYYY\u5e74M\u6708D\u65e5",lll:"YYYY\u5e74M\u6708D\u65e5 HH:mm",llll:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm"},meridiemParse:/\u51cc\u6668|\u65e9\u4e0a|\u4e0a\u5348|\u4e2d\u5348|\u4e0b\u5348|\u665a\u4e0a/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u51cc\u6668"===a||"\u65e9\u4e0a"===a||"\u4e0a\u5348"===a||"\u4e0b\u5348"!==a&&"\u665a\u4e0a"!==a&&11<=e?e:e+12},meridiem:function(e,a,t){e=100*e+a;return e<600?"\u51cc\u6668":e<900?"\u65e9\u4e0a":e<1130?"\u4e0a\u5348":e<1230?"\u4e2d\u5348":e<1800?"\u4e0b\u5348":"\u665a\u4e0a"},calendar:{sameDay:"[\u4eca\u5929]LT",nextDay:"[\u660e\u5929]LT",nextWeek:function(e){return e.week()!==this.week()?"[\u4e0b]dddLT":"[\u672c]dddLT"},lastDay:"[\u6628\u5929]LT",lastWeek:function(e){return this.week()!==e.week()?"[\u4e0a]dddLT":"[\u672c]dddLT"},sameElse:"L"},dayOfMonthOrdinalParse:/\d{1,2}(\u65e5|\u6708|\u5468)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"\u65e5";case"M":return e+"\u6708";case"w":case"W":return e+"\u5468";default:return e}},relativeTime:{future:"%s\u540e",past:"%s\u524d",s:"\u51e0\u79d2",ss:"%d \u79d2",m:"1 \u5206\u949f",mm:"%d \u5206\u949f",h:"1 \u5c0f\u65f6",hh:"%d \u5c0f\u65f6",d:"1 \u5929",dd:"%d \u5929",w:"1 \u5468",ww:"%d \u5468",M:"1 \u4e2a\u6708",MM:"%d \u4e2a\u6708",y:"1 \u5e74",yy:"%d \u5e74"},week:{dow:1,doy:4}}),M.defineLocale("zh-hk",{months:"\u4e00\u6708_\u4e8c\u6708_\u4e09\u6708_\u56db\u6708_\u4e94\u6708_\u516d\u6708_\u4e03\u6708_\u516b\u6708_\u4e5d\u6708_\u5341\u6708_\u5341\u4e00\u6708_\u5341\u4e8c\u6708".split("_"),monthsShort:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),weekdays:"\u661f\u671f\u65e5_\u661f\u671f\u4e00_\u661f\u671f\u4e8c_\u661f\u671f\u4e09_\u661f\u671f\u56db_\u661f\u671f\u4e94_\u661f\u671f\u516d".split("_"),weekdaysShort:"\u9031\u65e5_\u9031\u4e00_\u9031\u4e8c_\u9031\u4e09_\u9031\u56db_\u9031\u4e94_\u9031\u516d".split("_"),weekdaysMin:"\u65e5_\u4e00_\u4e8c_\u4e09_\u56db_\u4e94_\u516d".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY/MM/DD",LL:"YYYY\u5e74M\u6708D\u65e5",LLL:"YYYY\u5e74M\u6708D\u65e5 HH:mm",LLLL:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm",l:"YYYY/M/D",ll:"YYYY\u5e74M\u6708D\u65e5",lll:"YYYY\u5e74M\u6708D\u65e5 HH:mm",llll:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm"},meridiemParse:/\u51cc\u6668|\u65e9\u4e0a|\u4e0a\u5348|\u4e2d\u5348|\u4e0b\u5348|\u665a\u4e0a/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u51cc\u6668"===a||"\u65e9\u4e0a"===a||"\u4e0a\u5348"===a?e:"\u4e2d\u5348"===a?11<=e?e:e+12:"\u4e0b\u5348"===a||"\u665a\u4e0a"===a?e+12:void 0},meridiem:function(e,a,t){e=100*e+a;return e<600?"\u51cc\u6668":e<900?"\u65e9\u4e0a":e<1200?"\u4e0a\u5348":1200===e?"\u4e2d\u5348":e<1800?"\u4e0b\u5348":"\u665a\u4e0a"},calendar:{sameDay:"[\u4eca\u5929]LT",nextDay:"[\u660e\u5929]LT",nextWeek:"[\u4e0b]ddddLT",lastDay:"[\u6628\u5929]LT",lastWeek:"[\u4e0a]ddddLT",sameElse:"L"},dayOfMonthOrdinalParse:/\d{1,2}(\u65e5|\u6708|\u9031)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"\u65e5";case"M":return e+"\u6708";case"w":case"W":return e+"\u9031";default:return e}},relativeTime:{future:"%s\u5f8c",past:"%s\u524d",s:"\u5e7e\u79d2",ss:"%d \u79d2",m:"1 \u5206\u9418",mm:"%d \u5206\u9418",h:"1 \u5c0f\u6642",hh:"%d \u5c0f\u6642",d:"1 \u5929",dd:"%d \u5929",M:"1 \u500b\u6708",MM:"%d \u500b\u6708",y:"1 \u5e74",yy:"%d \u5e74"}}),M.defineLocale("zh-mo",{months:"\u4e00\u6708_\u4e8c\u6708_\u4e09\u6708_\u56db\u6708_\u4e94\u6708_\u516d\u6708_\u4e03\u6708_\u516b\u6708_\u4e5d\u6708_\u5341\u6708_\u5341\u4e00\u6708_\u5341\u4e8c\u6708".split("_"),monthsShort:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),weekdays:"\u661f\u671f\u65e5_\u661f\u671f\u4e00_\u661f\u671f\u4e8c_\u661f\u671f\u4e09_\u661f\u671f\u56db_\u661f\u671f\u4e94_\u661f\u671f\u516d".split("_"),weekdaysShort:"\u9031\u65e5_\u9031\u4e00_\u9031\u4e8c_\u9031\u4e09_\u9031\u56db_\u9031\u4e94_\u9031\u516d".split("_"),weekdaysMin:"\u65e5_\u4e00_\u4e8c_\u4e09_\u56db_\u4e94_\u516d".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"DD/MM/YYYY",LL:"YYYY\u5e74M\u6708D\u65e5",LLL:"YYYY\u5e74M\u6708D\u65e5 HH:mm",LLLL:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm",l:"D/M/YYYY",ll:"YYYY\u5e74M\u6708D\u65e5",lll:"YYYY\u5e74M\u6708D\u65e5 HH:mm",llll:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm"},meridiemParse:/\u51cc\u6668|\u65e9\u4e0a|\u4e0a\u5348|\u4e2d\u5348|\u4e0b\u5348|\u665a\u4e0a/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u51cc\u6668"===a||"\u65e9\u4e0a"===a||"\u4e0a\u5348"===a?e:"\u4e2d\u5348"===a?11<=e?e:e+12:"\u4e0b\u5348"===a||"\u665a\u4e0a"===a?e+12:void 0},meridiem:function(e,a,t){e=100*e+a;return e<600?"\u51cc\u6668":e<900?"\u65e9\u4e0a":e<1130?"\u4e0a\u5348":e<1230?"\u4e2d\u5348":e<1800?"\u4e0b\u5348":"\u665a\u4e0a"},calendar:{sameDay:"[\u4eca\u5929] LT",nextDay:"[\u660e\u5929] LT",nextWeek:"[\u4e0b]dddd LT",lastDay:"[\u6628\u5929] LT",lastWeek:"[\u4e0a]dddd LT",sameElse:"L"},dayOfMonthOrdinalParse:/\d{1,2}(\u65e5|\u6708|\u9031)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"\u65e5";case"M":return e+"\u6708";case"w":case"W":return e+"\u9031";default:return e}},relativeTime:{future:"%s\u5167",past:"%s\u524d",s:"\u5e7e\u79d2",ss:"%d \u79d2",m:"1 \u5206\u9418",mm:"%d \u5206\u9418",h:"1 \u5c0f\u6642",hh:"%d \u5c0f\u6642",d:"1 \u5929",dd:"%d \u5929",M:"1 \u500b\u6708",MM:"%d \u500b\u6708",y:"1 \u5e74",yy:"%d \u5e74"}}),M.defineLocale("zh-tw",{months:"\u4e00\u6708_\u4e8c\u6708_\u4e09\u6708_\u56db\u6708_\u4e94\u6708_\u516d\u6708_\u4e03\u6708_\u516b\u6708_\u4e5d\u6708_\u5341\u6708_\u5341\u4e00\u6708_\u5341\u4e8c\u6708".split("_"),monthsShort:"1\u6708_2\u6708_3\u6708_4\u6708_5\u6708_6\u6708_7\u6708_8\u6708_9\u6708_10\u6708_11\u6708_12\u6708".split("_"),weekdays:"\u661f\u671f\u65e5_\u661f\u671f\u4e00_\u661f\u671f\u4e8c_\u661f\u671f\u4e09_\u661f\u671f\u56db_\u661f\u671f\u4e94_\u661f\u671f\u516d".split("_"),weekdaysShort:"\u9031\u65e5_\u9031\u4e00_\u9031\u4e8c_\u9031\u4e09_\u9031\u56db_\u9031\u4e94_\u9031\u516d".split("_"),weekdaysMin:"\u65e5_\u4e00_\u4e8c_\u4e09_\u56db_\u4e94_\u516d".split("_"),longDateFormat:{LT:"HH:mm",LTS:"HH:mm:ss",L:"YYYY/MM/DD",LL:"YYYY\u5e74M\u6708D\u65e5",LLL:"YYYY\u5e74M\u6708D\u65e5 HH:mm",LLLL:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm",l:"YYYY/M/D",ll:"YYYY\u5e74M\u6708D\u65e5",lll:"YYYY\u5e74M\u6708D\u65e5 HH:mm",llll:"YYYY\u5e74M\u6708D\u65e5dddd HH:mm"},meridiemParse:/\u51cc\u6668|\u65e9\u4e0a|\u4e0a\u5348|\u4e2d\u5348|\u4e0b\u5348|\u665a\u4e0a/,meridiemHour:function(e,a){return 12===e&&(e=0),"\u51cc\u6668"===a||"\u65e9\u4e0a"===a||"\u4e0a\u5348"===a?e:"\u4e2d\u5348"===a?11<=e?e:e+12:"\u4e0b\u5348"===a||"\u665a\u4e0a"===a?e+12:void 0},meridiem:function(e,a,t){e=100*e+a;return e<600?"\u51cc\u6668":e<900?"\u65e9\u4e0a":e<1130?"\u4e0a\u5348":e<1230?"\u4e2d\u5348":e<1800?"\u4e0b\u5348":"\u665a\u4e0a"},calendar:{sameDay:"[\u4eca\u5929] LT",nextDay:"[\u660e\u5929] LT",nextWeek:"[\u4e0b]dddd LT",lastDay:"[\u6628\u5929] LT",lastWeek:"[\u4e0a]dddd LT",sameElse:"L"},dayOfMonthOrdinalParse:/\d{1,2}(\u65e5|\u6708|\u9031)/,ordinal:function(e,a){switch(a){case"d":case"D":case"DDD":return e+"\u65e5";case"M":return e+"\u6708";case"w":case"W":return e+"\u9031";default:return e}},relativeTime:{future:"%s\u5f8c",past:"%s\u524d",s:"\u5e7e\u79d2",ss:"%d \u79d2",m:"1 \u5206\u9418",mm:"%d \u5206\u9418",h:"1 \u5c0f\u6642",hh:"%d \u5c0f\u6642",d:"1 \u5929",dd:"%d \u5929",M:"1 \u500b\u6708",MM:"%d \u500b\u6708",y:"1 \u5e74",yy:"%d \u5e74"}}),M.locale("en"),M});
//# sourceMappingURL=moment-with-locales.min.js.map
/**
 * @license
 * Lodash <https://lodash.com/>
 * Copyright OpenJS Foundation and other contributors <https://openjsf.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */
;(function() {

  /** Used as a safe reference for `undefined` in pre-ES5 environments. */
  var undefined;

  /** Used as the semantic version number. */
  var VERSION = '4.17.23';

  /** Used as the size to enable large array optimizations. */
  var LARGE_ARRAY_SIZE = 200;

  /** Error message constants. */
  var CORE_ERROR_TEXT = 'Unsupported core-js use. Try https://npms.io/search?q=ponyfill.',
      FUNC_ERROR_TEXT = 'Expected a function',
      INVALID_TEMPL_VAR_ERROR_TEXT = 'Invalid `variable` option passed into `_.template`';

  /** Used to stand-in for `undefined` hash values. */
  var HASH_UNDEFINED = '__lodash_hash_undefined__';

  /** Used as the maximum memoize cache size. */
  var MAX_MEMOIZE_SIZE = 500;

  /** Used as the internal argument placeholder. */
  var PLACEHOLDER = '__lodash_placeholder__';

  /** Used to compose bitmasks for cloning. */
  var CLONE_DEEP_FLAG = 1,
      CLONE_FLAT_FLAG = 2,
      CLONE_SYMBOLS_FLAG = 4;

  /** Used to compose bitmasks for value comparisons. */
  var COMPARE_PARTIAL_FLAG = 1,
      COMPARE_UNORDERED_FLAG = 2;

  /** Used to compose bitmasks for function metadata. */
  var WRAP_BIND_FLAG = 1,
      WRAP_BIND_KEY_FLAG = 2,
      WRAP_CURRY_BOUND_FLAG = 4,
      WRAP_CURRY_FLAG = 8,
      WRAP_CURRY_RIGHT_FLAG = 16,
      WRAP_PARTIAL_FLAG = 32,
      WRAP_PARTIAL_RIGHT_FLAG = 64,
      WRAP_ARY_FLAG = 128,
      WRAP_REARG_FLAG = 256,
      WRAP_FLIP_FLAG = 512;

  /** Used as default options for `_.truncate`. */
  var DEFAULT_TRUNC_LENGTH = 30,
      DEFAULT_TRUNC_OMISSION = '...';

  /** Used to detect hot functions by number of calls within a span of milliseconds. */
  var HOT_COUNT = 800,
      HOT_SPAN = 16;

  /** Used to indicate the type of lazy iteratees. */
  var LAZY_FILTER_FLAG = 1,
      LAZY_MAP_FLAG = 2,
      LAZY_WHILE_FLAG = 3;

  /** Used as references for various `Number` constants. */
  var INFINITY = 1 / 0,
      MAX_SAFE_INTEGER = 9007199254740991,
      MAX_INTEGER = 1.7976931348623157e+308,
      NAN = 0 / 0;

  /** Used as references for the maximum length and index of an array. */
  var MAX_ARRAY_LENGTH = 4294967295,
      MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1,
      HALF_MAX_ARRAY_LENGTH = MAX_ARRAY_LENGTH >>> 1;

  /** Used to associate wrap methods with their bit flags. */
  var wrapFlags = [
    ['ary', WRAP_ARY_FLAG],
    ['bind', WRAP_BIND_FLAG],
    ['bindKey', WRAP_BIND_KEY_FLAG],
    ['curry', WRAP_CURRY_FLAG],
    ['curryRight', WRAP_CURRY_RIGHT_FLAG],
    ['flip', WRAP_FLIP_FLAG],
    ['partial', WRAP_PARTIAL_FLAG],
    ['partialRight', WRAP_PARTIAL_RIGHT_FLAG],
    ['rearg', WRAP_REARG_FLAG]
  ];

  /** `Object#toString` result references. */
  var argsTag = '[object Arguments]',
      arrayTag = '[object Array]',
      asyncTag = '[object AsyncFunction]',
      boolTag = '[object Boolean]',
      dateTag = '[object Date]',
      domExcTag = '[object DOMException]',
      errorTag = '[object Error]',
      funcTag = '[object Function]',
      genTag = '[object GeneratorFunction]',
      mapTag = '[object Map]',
      numberTag = '[object Number]',
      nullTag = '[object Null]',
      objectTag = '[object Object]',
      promiseTag = '[object Promise]',
      proxyTag = '[object Proxy]',
      regexpTag = '[object RegExp]',
      setTag = '[object Set]',
      stringTag = '[object String]',
      symbolTag = '[object Symbol]',
      undefinedTag = '[object Undefined]',
      weakMapTag = '[object WeakMap]',
      weakSetTag = '[object WeakSet]';

  var arrayBufferTag = '[object ArrayBuffer]',
      dataViewTag = '[object DataView]',
      float32Tag = '[object Float32Array]',
      float64Tag = '[object Float64Array]',
      int8Tag = '[object Int8Array]',
      int16Tag = '[object Int16Array]',
      int32Tag = '[object Int32Array]',
      uint8Tag = '[object Uint8Array]',
      uint8ClampedTag = '[object Uint8ClampedArray]',
      uint16Tag = '[object Uint16Array]',
      uint32Tag = '[object Uint32Array]';

  /** Used to match empty string literals in compiled template source. */
  var reEmptyStringLeading = /\b__p \+= '';/g,
      reEmptyStringMiddle = /\b(__p \+=) '' \+/g,
      reEmptyStringTrailing = /(__e\(.*?\)|\b__t\)) \+\n'';/g;

  /** Used to match HTML entities and HTML characters. */
  var reEscapedHtml = /&(?:amp|lt|gt|quot|#39);/g,
      reUnescapedHtml = /[&<>"']/g,
      reHasEscapedHtml = RegExp(reEscapedHtml.source),
      reHasUnescapedHtml = RegExp(reUnescapedHtml.source);

  /** Used to match template delimiters. */
  var reEscape = /<%-([\s\S]+?)%>/g,
      reEvaluate = /<%([\s\S]+?)%>/g,
      reInterpolate = /<%=([\s\S]+?)%>/g;

  /** Used to match property names within property paths. */
  var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
      reIsPlainProp = /^\w*$/,
      rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

  /**
   * Used to match `RegExp`
   * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
   */
  var reRegExpChar = /[\\^$.*+?()[\]{}|]/g,
      reHasRegExpChar = RegExp(reRegExpChar.source);

  /** Used to match leading whitespace. */
  var reTrimStart = /^\s+/;

  /** Used to match a single whitespace character. */
  var reWhitespace = /\s/;

  /** Used to match wrap detail comments. */
  var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/,
      reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
      reSplitDetails = /,? & /;

  /** Used to match words composed of alphanumeric characters. */
  var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

  /**
   * Used to validate the `validate` option in `_.template` variable.
   *
   * Forbids characters which could potentially change the meaning of the function argument definition:
   * - "()," (modification of function parameters)
   * - "=" (default value)
   * - "[]{}" (destructuring of function parameters)
   * - "/" (beginning of a comment)
   * - whitespace
   */
  var reForbiddenIdentifierChars = /[()=,{}\[\]\/\s]/;

  /** Used to match backslashes in property paths. */
  var reEscapeChar = /\\(\\)?/g;

  /**
   * Used to match
   * [ES template delimiters](http://ecma-international.org/ecma-262/7.0/#sec-template-literal-lexical-components).
   */
  var reEsTemplate = /\$\{([^\\}]*(?:\\.[^\\}]*)*)\}/g;

  /** Used to match `RegExp` flags from their coerced string values. */
  var reFlags = /\w*$/;

  /** Used to detect bad signed hexadecimal string values. */
  var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

  /** Used to detect binary string values. */
  var reIsBinary = /^0b[01]+$/i;

  /** Used to detect host constructors (Safari). */
  var reIsHostCtor = /^\[object .+?Constructor\]$/;

  /** Used to detect octal string values. */
  var reIsOctal = /^0o[0-7]+$/i;

  /** Used to detect unsigned integer values. */
  var reIsUint = /^(?:0|[1-9]\d*)$/;

  /** Used to match Latin Unicode letters (excluding mathematical operators). */
  var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

  /** Used to ensure capturing order of template delimiters. */
  var reNoMatch = /($^)/;

  /** Used to match unescaped characters in compiled string literals. */
  var reUnescapedString = /['\n\r\u2028\u2029\\]/g;

  /** Used to compose unicode character classes. */
  var rsAstralRange = '\\ud800-\\udfff',
      rsComboMarksRange = '\\u0300-\\u036f',
      reComboHalfMarksRange = '\\ufe20-\\ufe2f',
      rsComboSymbolsRange = '\\u20d0-\\u20ff',
      rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
      rsDingbatRange = '\\u2700-\\u27bf',
      rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
      rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
      rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
      rsPunctuationRange = '\\u2000-\\u206f',
      rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
      rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
      rsVarRange = '\\ufe0e\\ufe0f',
      rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

  /** Used to compose unicode capture groups. */
  var rsApos = "['\u2019]",
      rsAstral = '[' + rsAstralRange + ']',
      rsBreak = '[' + rsBreakRange + ']',
      rsCombo = '[' + rsComboRange + ']',
      rsDigits = '\\d+',
      rsDingbat = '[' + rsDingbatRange + ']',
      rsLower = '[' + rsLowerRange + ']',
      rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
      rsFitz = '\\ud83c[\\udffb-\\udfff]',
      rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
      rsNonAstral = '[^' + rsAstralRange + ']',
      rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
      rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
      rsUpper = '[' + rsUpperRange + ']',
      rsZWJ = '\\u200d';

  /** Used to compose unicode regexes. */
  var rsMiscLower = '(?:' + rsLower + '|' + rsMisc + ')',
      rsMiscUpper = '(?:' + rsUpper + '|' + rsMisc + ')',
      rsOptContrLower = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
      rsOptContrUpper = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
      reOptMod = rsModifier + '?',
      rsOptVar = '[' + rsVarRange + ']?',
      rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
      rsOrdLower = '\\d*(?:1st|2nd|3rd|(?![123])\\dth)(?=\\b|[A-Z_])',
      rsOrdUpper = '\\d*(?:1ST|2ND|3RD|(?![123])\\dTH)(?=\\b|[a-z_])',
      rsSeq = rsOptVar + reOptMod + rsOptJoin,
      rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq,
      rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

  /** Used to match apostrophes. */
  var reApos = RegExp(rsApos, 'g');

  /**
   * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
   * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
   */
  var reComboMark = RegExp(rsCombo, 'g');

  /** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
  var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

  /** Used to match complex or compound words. */
  var reUnicodeWord = RegExp([
    rsUpper + '?' + rsLower + '+' + rsOptContrLower + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
    rsMiscUpper + '+' + rsOptContrUpper + '(?=' + [rsBreak, rsUpper + rsMiscLower, '$'].join('|') + ')',
    rsUpper + '?' + rsMiscLower + '+' + rsOptContrLower,
    rsUpper + '+' + rsOptContrUpper,
    rsOrdUpper,
    rsOrdLower,
    rsDigits,
    rsEmoji
  ].join('|'), 'g');

  /** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
  var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

  /** Used to detect strings that need a more robust regexp to match words. */
  var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

  /** Used to assign default `context` object properties. */
  var contextProps = [
    'Array', 'Buffer', 'DataView', 'Date', 'Error', 'Float32Array', 'Float64Array',
    'Function', 'Int8Array', 'Int16Array', 'Int32Array', 'Map', 'Math', 'Object',
    'Promise', 'RegExp', 'Set', 'String', 'Symbol', 'TypeError', 'Uint8Array',
    'Uint8ClampedArray', 'Uint16Array', 'Uint32Array', 'WeakMap',
    '_', 'clearTimeout', 'isFinite', 'parseInt', 'setTimeout'
  ];

  /** Used to make template sourceURLs easier to identify. */
  var templateCounter = -1;

  /** Used to identify `toStringTag` values of typed arrays. */
  var typedArrayTags = {};
  typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
  typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
  typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
  typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
  typedArrayTags[uint32Tag] = true;
  typedArrayTags[argsTag] = typedArrayTags[arrayTag] =
  typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
  typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
  typedArrayTags[errorTag] = typedArrayTags[funcTag] =
  typedArrayTags[mapTag] = typedArrayTags[numberTag] =
  typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
  typedArrayTags[setTag] = typedArrayTags[stringTag] =
  typedArrayTags[weakMapTag] = false;

  /** Used to identify `toStringTag` values supported by `_.clone`. */
  var cloneableTags = {};
  cloneableTags[argsTag] = cloneableTags[arrayTag] =
  cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
  cloneableTags[boolTag] = cloneableTags[dateTag] =
  cloneableTags[float32Tag] = cloneableTags[float64Tag] =
  cloneableTags[int8Tag] = cloneableTags[int16Tag] =
  cloneableTags[int32Tag] = cloneableTags[mapTag] =
  cloneableTags[numberTag] = cloneableTags[objectTag] =
  cloneableTags[regexpTag] = cloneableTags[setTag] =
  cloneableTags[stringTag] = cloneableTags[symbolTag] =
  cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
  cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
  cloneableTags[errorTag] = cloneableTags[funcTag] =
  cloneableTags[weakMapTag] = false;

  /** Used to map Latin Unicode letters to basic Latin letters. */
  var deburredLetters = {
    // Latin-1 Supplement block.
    '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
    '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
    '\xc7': 'C',  '\xe7': 'c',
    '\xd0': 'D',  '\xf0': 'd',
    '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
    '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
    '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
    '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
    '\xd1': 'N',  '\xf1': 'n',
    '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
    '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
    '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
    '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
    '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
    '\xc6': 'Ae', '\xe6': 'ae',
    '\xde': 'Th', '\xfe': 'th',
    '\xdf': 'ss',
    // Latin Extended-A block.
    '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
    '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
    '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
    '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
    '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
    '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
    '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
    '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
    '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
    '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
    '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
    '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
    '\u0134': 'J',  '\u0135': 'j',
    '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
    '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
    '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
    '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
    '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
    '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
    '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
    '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
    '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
    '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
    '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
    '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
    '\u0163': 't',  '\u0165': 't', '\u0167': 't',
    '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
    '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
    '\u0174': 'W',  '\u0175': 'w',
    '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
    '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
    '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
    '\u0132': 'IJ', '\u0133': 'ij',
    '\u0152': 'Oe', '\u0153': 'oe',
    '\u0149': "'n", '\u017f': 's'
  };

  /** Used to map characters to HTML entities. */
  var htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  };

  /** Used to map HTML entities to characters. */
  var htmlUnescapes = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'"
  };

  /** Used to escape characters for inclusion in compiled string literals. */
  var stringEscapes = {
    '\\': '\\',
    "'": "'",
    '\n': 'n',
    '\r': 'r',
    '\u2028': 'u2028',
    '\u2029': 'u2029'
  };

  /** Built-in method references without a dependency on `root`. */
  var freeParseFloat = parseFloat,
      freeParseInt = parseInt;

  /** Detect free variable `global` from Node.js. */
  var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

  /** Detect free variable `self`. */
  var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

  /** Used as a reference to the global object. */
  var root = freeGlobal || freeSelf || Function('return this')();

  /** Detect free variable `exports`. */
  var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

  /** Detect free variable `module`. */
  var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

  /** Detect the popular CommonJS extension `module.exports`. */
  var moduleExports = freeModule && freeModule.exports === freeExports;

  /** Detect free variable `process` from Node.js. */
  var freeProcess = moduleExports && freeGlobal.process;

  /** Used to access faster Node.js helpers. */
  var nodeUtil = (function() {
    try {
      // Use `util.types` for Node.js 10+.
      var types = freeModule && freeModule.require && freeModule.require('util').types;

      if (types) {
        return types;
      }

      // Legacy `process.binding('util')` for Node.js < 10.
      return freeProcess && freeProcess.binding && freeProcess.binding('util');
    } catch (e) {}
  }());

  /* Node.js helper references. */
  var nodeIsArrayBuffer = nodeUtil && nodeUtil.isArrayBuffer,
      nodeIsDate = nodeUtil && nodeUtil.isDate,
      nodeIsMap = nodeUtil && nodeUtil.isMap,
      nodeIsRegExp = nodeUtil && nodeUtil.isRegExp,
      nodeIsSet = nodeUtil && nodeUtil.isSet,
      nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

  /*--------------------------------------------------------------------------*/

  /**
   * A faster alternative to `Function#apply`, this function invokes `func`
   * with the `this` binding of `thisArg` and the arguments of `args`.
   *
   * @private
   * @param {Function} func The function to invoke.
   * @param {*} thisArg The `this` binding of `func`.
   * @param {Array} args The arguments to invoke `func` with.
   * @returns {*} Returns the result of `func`.
   */
  function apply(func, thisArg, args) {
    switch (args.length) {
      case 0: return func.call(thisArg);
      case 1: return func.call(thisArg, args[0]);
      case 2: return func.call(thisArg, args[0], args[1]);
      case 3: return func.call(thisArg, args[0], args[1], args[2]);
    }
    return func.apply(thisArg, args);
  }

  /**
   * A specialized version of `baseAggregator` for arrays.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} setter The function to set `accumulator` values.
   * @param {Function} iteratee The iteratee to transform keys.
   * @param {Object} accumulator The initial aggregated object.
   * @returns {Function} Returns `accumulator`.
   */
  function arrayAggregator(array, setter, iteratee, accumulator) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      var value = array[index];
      setter(accumulator, value, iteratee(value), array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.forEach` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEach(array, iteratee) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (iteratee(array[index], index, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.forEachRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns `array`.
   */
  function arrayEachRight(array, iteratee) {
    var length = array == null ? 0 : array.length;

    while (length--) {
      if (iteratee(array[length], length, array) === false) {
        break;
      }
    }
    return array;
  }

  /**
   * A specialized version of `_.every` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if all elements pass the predicate check,
   *  else `false`.
   */
  function arrayEvery(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (!predicate(array[index], index, array)) {
        return false;
      }
    }
    return true;
  }

  /**
   * A specialized version of `_.filter` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {Array} Returns the new filtered array.
   */
  function arrayFilter(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (predicate(value, index, array)) {
        result[resIndex++] = value;
      }
    }
    return result;
  }

  /**
   * A specialized version of `_.includes` for arrays without support for
   * specifying an index to search from.
   *
   * @private
   * @param {Array} [array] The array to inspect.
   * @param {*} target The value to search for.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludes(array, value) {
    var length = array == null ? 0 : array.length;
    return !!length && baseIndexOf(array, value, 0) > -1;
  }

  /**
   * This function is like `arrayIncludes` except that it accepts a comparator.
   *
   * @private
   * @param {Array} [array] The array to inspect.
   * @param {*} target The value to search for.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {boolean} Returns `true` if `target` is found, else `false`.
   */
  function arrayIncludesWith(array, value, comparator) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (comparator(value, array[index])) {
        return true;
      }
    }
    return false;
  }

  /**
   * A specialized version of `_.map` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the new mapped array.
   */
  function arrayMap(array, iteratee) {
    var index = -1,
        length = array == null ? 0 : array.length,
        result = Array(length);

    while (++index < length) {
      result[index] = iteratee(array[index], index, array);
    }
    return result;
  }

  /**
   * Appends the elements of `values` to `array`.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {Array} values The values to append.
   * @returns {Array} Returns `array`.
   */
  function arrayPush(array, values) {
    var index = -1,
        length = values.length,
        offset = array.length;

    while (++index < length) {
      array[offset + index] = values[index];
    }
    return array;
  }

  /**
   * A specialized version of `_.reduce` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the first element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduce(array, iteratee, accumulator, initAccum) {
    var index = -1,
        length = array == null ? 0 : array.length;

    if (initAccum && length) {
      accumulator = array[++index];
    }
    while (++index < length) {
      accumulator = iteratee(accumulator, array[index], index, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.reduceRight` for arrays without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} [accumulator] The initial value.
   * @param {boolean} [initAccum] Specify using the last element of `array` as
   *  the initial value.
   * @returns {*} Returns the accumulated value.
   */
  function arrayReduceRight(array, iteratee, accumulator, initAccum) {
    var length = array == null ? 0 : array.length;
    if (initAccum && length) {
      accumulator = array[--length];
    }
    while (length--) {
      accumulator = iteratee(accumulator, array[length], length, array);
    }
    return accumulator;
  }

  /**
   * A specialized version of `_.some` for arrays without support for iteratee
   * shorthands.
   *
   * @private
   * @param {Array} [array] The array to iterate over.
   * @param {Function} predicate The function invoked per iteration.
   * @returns {boolean} Returns `true` if any element passes the predicate check,
   *  else `false`.
   */
  function arraySome(array, predicate) {
    var index = -1,
        length = array == null ? 0 : array.length;

    while (++index < length) {
      if (predicate(array[index], index, array)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Gets the size of an ASCII `string`.
   *
   * @private
   * @param {string} string The string inspect.
   * @returns {number} Returns the string size.
   */
  var asciiSize = baseProperty('length');

  /**
   * Converts an ASCII `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function asciiToArray(string) {
    return string.split('');
  }

  /**
   * Splits an ASCII `string` into an array of its words.
   *
   * @private
   * @param {string} The string to inspect.
   * @returns {Array} Returns the words of `string`.
   */
  function asciiWords(string) {
    return string.match(reAsciiWord) || [];
  }

  /**
   * The base implementation of methods like `_.findKey` and `_.findLastKey`,
   * without support for iteratee shorthands, which iterates over `collection`
   * using `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to inspect.
   * @param {Function} predicate The function invoked per iteration.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @returns {*} Returns the found element or its key, else `undefined`.
   */
  function baseFindKey(collection, predicate, eachFunc) {
    var result;
    eachFunc(collection, function(value, key, collection) {
      if (predicate(value, key, collection)) {
        result = key;
        return false;
      }
    });
    return result;
  }

  /**
   * The base implementation of `_.findIndex` and `_.findLastIndex` without
   * support for iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {Function} predicate The function invoked per iteration.
   * @param {number} fromIndex The index to search from.
   * @param {boolean} [fromRight] Specify iterating from right to left.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseFindIndex(array, predicate, fromIndex, fromRight) {
    var length = array.length,
        index = fromIndex + (fromRight ? 1 : -1);

    while ((fromRight ? index-- : ++index < length)) {
      if (predicate(array[index], index, array)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOf(array, value, fromIndex) {
    return value === value
      ? strictIndexOf(array, value, fromIndex)
      : baseFindIndex(array, baseIsNaN, fromIndex);
  }

  /**
   * This function is like `baseIndexOf` except that it accepts a comparator.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @param {Function} comparator The comparator invoked per element.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function baseIndexOfWith(array, value, fromIndex, comparator) {
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (comparator(array[index], value)) {
        return index;
      }
    }
    return -1;
  }

  /**
   * The base implementation of `_.isNaN` without support for number objects.
   *
   * @private
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
   */
  function baseIsNaN(value) {
    return value !== value;
  }

  /**
   * The base implementation of `_.mean` and `_.meanBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the mean.
   */
  function baseMean(array, iteratee) {
    var length = array == null ? 0 : array.length;
    return length ? (baseSum(array, iteratee) / length) : NAN;
  }

  /**
   * The base implementation of `_.property` without support for deep paths.
   *
   * @private
   * @param {string} key The key of the property to get.
   * @returns {Function} Returns the new accessor function.
   */
  function baseProperty(key) {
    return function(object) {
      return object == null ? undefined : object[key];
    };
  }

  /**
   * The base implementation of `_.propertyOf` without support for deep paths.
   *
   * @private
   * @param {Object} object The object to query.
   * @returns {Function} Returns the new accessor function.
   */
  function basePropertyOf(object) {
    return function(key) {
      return object == null ? undefined : object[key];
    };
  }

  /**
   * The base implementation of `_.reduce` and `_.reduceRight`, without support
   * for iteratee shorthands, which iterates over `collection` using `eachFunc`.
   *
   * @private
   * @param {Array|Object} collection The collection to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @param {*} accumulator The initial value.
   * @param {boolean} initAccum Specify using the first or last element of
   *  `collection` as the initial value.
   * @param {Function} eachFunc The function to iterate over `collection`.
   * @returns {*} Returns the accumulated value.
   */
  function baseReduce(collection, iteratee, accumulator, initAccum, eachFunc) {
    eachFunc(collection, function(value, index, collection) {
      accumulator = initAccum
        ? (initAccum = false, value)
        : iteratee(accumulator, value, index, collection);
    });
    return accumulator;
  }

  /**
   * The base implementation of `_.sortBy` which uses `comparer` to define the
   * sort order of `array` and replaces criteria objects with their corresponding
   * values.
   *
   * @private
   * @param {Array} array The array to sort.
   * @param {Function} comparer The function to define sort order.
   * @returns {Array} Returns `array`.
   */
  function baseSortBy(array, comparer) {
    var length = array.length;

    array.sort(comparer);
    while (length--) {
      array[length] = array[length].value;
    }
    return array;
  }

  /**
   * The base implementation of `_.sum` and `_.sumBy` without support for
   * iteratee shorthands.
   *
   * @private
   * @param {Array} array The array to iterate over.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {number} Returns the sum.
   */
  function baseSum(array, iteratee) {
    var result,
        index = -1,
        length = array.length;

    while (++index < length) {
      var current = iteratee(array[index]);
      if (current !== undefined) {
        result = result === undefined ? current : (result + current);
      }
    }
    return result;
  }

  /**
   * The base implementation of `_.times` without support for iteratee shorthands
   * or max array length checks.
   *
   * @private
   * @param {number} n The number of times to invoke `iteratee`.
   * @param {Function} iteratee The function invoked per iteration.
   * @returns {Array} Returns the array of results.
   */
  function baseTimes(n, iteratee) {
    var index = -1,
        result = Array(n);

    while (++index < n) {
      result[index] = iteratee(index);
    }
    return result;
  }

  /**
   * The base implementation of `_.toPairs` and `_.toPairsIn` which creates an array
   * of key-value pairs for `object` corresponding to the property names of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the key-value pairs.
   */
  function baseToPairs(object, props) {
    return arrayMap(props, function(key) {
      return [key, object[key]];
    });
  }

  /**
   * The base implementation of `_.trim`.
   *
   * @private
   * @param {string} string The string to trim.
   * @returns {string} Returns the trimmed string.
   */
  function baseTrim(string) {
    return string
      ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
      : string;
  }

  /**
   * The base implementation of `_.unary` without support for storing metadata.
   *
   * @private
   * @param {Function} func The function to cap arguments for.
   * @returns {Function} Returns the new capped function.
   */
  function baseUnary(func) {
    return function(value) {
      return func(value);
    };
  }

  /**
   * The base implementation of `_.values` and `_.valuesIn` which creates an
   * array of `object` property values corresponding to the property names
   * of `props`.
   *
   * @private
   * @param {Object} object The object to query.
   * @param {Array} props The property names to get values for.
   * @returns {Object} Returns the array of property values.
   */
  function baseValues(object, props) {
    return arrayMap(props, function(key) {
      return object[key];
    });
  }

  /**
   * Checks if a `cache` value for `key` exists.
   *
   * @private
   * @param {Object} cache The cache to query.
   * @param {string} key The key of the entry to check.
   * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
   */
  function cacheHas(cache, key) {
    return cache.has(key);
  }

  /**
   * Used by `_.trim` and `_.trimStart` to get the index of the first string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the first unmatched string symbol.
   */
  function charsStartIndex(strSymbols, chrSymbols) {
    var index = -1,
        length = strSymbols.length;

    while (++index < length && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Used by `_.trim` and `_.trimEnd` to get the index of the last string symbol
   * that is not found in the character symbols.
   *
   * @private
   * @param {Array} strSymbols The string symbols to inspect.
   * @param {Array} chrSymbols The character symbols to find.
   * @returns {number} Returns the index of the last unmatched string symbol.
   */
  function charsEndIndex(strSymbols, chrSymbols) {
    var index = strSymbols.length;

    while (index-- && baseIndexOf(chrSymbols, strSymbols[index], 0) > -1) {}
    return index;
  }

  /**
   * Gets the number of `placeholder` occurrences in `array`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} placeholder The placeholder to search for.
   * @returns {number} Returns the placeholder count.
   */
  function countHolders(array, placeholder) {
    var length = array.length,
        result = 0;

    while (length--) {
      if (array[length] === placeholder) {
        ++result;
      }
    }
    return result;
  }

  /**
   * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
   * letters to basic Latin letters.
   *
   * @private
   * @param {string} letter The matched letter to deburr.
   * @returns {string} Returns the deburred letter.
   */
  var deburrLetter = basePropertyOf(deburredLetters);

  /**
   * Used by `_.escape` to convert characters to HTML entities.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  var escapeHtmlChar = basePropertyOf(htmlEscapes);

  /**
   * Used by `_.template` to escape characters for inclusion in compiled string literals.
   *
   * @private
   * @param {string} chr The matched character to escape.
   * @returns {string} Returns the escaped character.
   */
  function escapeStringChar(chr) {
    return '\\' + stringEscapes[chr];
  }

  /**
   * Gets the value at `key` of `object`.
   *
   * @private
   * @param {Object} [object] The object to query.
   * @param {string} key The key of the property to get.
   * @returns {*} Returns the property value.
   */
  function getValue(object, key) {
    return object == null ? undefined : object[key];
  }

  /**
   * Checks if `string` contains Unicode symbols.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {boolean} Returns `true` if a symbol is found, else `false`.
   */
  function hasUnicode(string) {
    return reHasUnicode.test(string);
  }

  /**
   * Checks if `string` contains a word composed of Unicode symbols.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {boolean} Returns `true` if a word is found, else `false`.
   */
  function hasUnicodeWord(string) {
    return reHasUnicodeWord.test(string);
  }

  /**
   * Converts `iterator` to an array.
   *
   * @private
   * @param {Object} iterator The iterator to convert.
   * @returns {Array} Returns the converted array.
   */
  function iteratorToArray(iterator) {
    var data,
        result = [];

    while (!(data = iterator.next()).done) {
      result.push(data.value);
    }
    return result;
  }

  /**
   * Converts `map` to its key-value pairs.
   *
   * @private
   * @param {Object} map The map to convert.
   * @returns {Array} Returns the key-value pairs.
   */
  function mapToArray(map) {
    var index = -1,
        result = Array(map.size);

    map.forEach(function(value, key) {
      result[++index] = [key, value];
    });
    return result;
  }

  /**
   * Creates a unary function that invokes `func` with its argument transformed.
   *
   * @private
   * @param {Function} func The function to wrap.
   * @param {Function} transform The argument transform.
   * @returns {Function} Returns the new function.
   */
  function overArg(func, transform) {
    return function(arg) {
      return func(transform(arg));
    };
  }

  /**
   * Replaces all `placeholder` elements in `array` with an internal placeholder
   * and returns an array of their indexes.
   *
   * @private
   * @param {Array} array The array to modify.
   * @param {*} placeholder The placeholder to replace.
   * @returns {Array} Returns the new array of placeholder indexes.
   */
  function replaceHolders(array, placeholder) {
    var index = -1,
        length = array.length,
        resIndex = 0,
        result = [];

    while (++index < length) {
      var value = array[index];
      if (value === placeholder || value === PLACEHOLDER) {
        array[index] = PLACEHOLDER;
        result[resIndex++] = index;
      }
    }
    return result;
  }

  /**
   * Converts `set` to an array of its values.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the values.
   */
  function setToArray(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = value;
    });
    return result;
  }

  /**
   * Converts `set` to its value-value pairs.
   *
   * @private
   * @param {Object} set The set to convert.
   * @returns {Array} Returns the value-value pairs.
   */
  function setToPairs(set) {
    var index = -1,
        result = Array(set.size);

    set.forEach(function(value) {
      result[++index] = [value, value];
    });
    return result;
  }

  /**
   * A specialized version of `_.indexOf` which performs strict equality
   * comparisons of values, i.e. `===`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function strictIndexOf(array, value, fromIndex) {
    var index = fromIndex - 1,
        length = array.length;

    while (++index < length) {
      if (array[index] === value) {
        return index;
      }
    }
    return -1;
  }

  /**
   * A specialized version of `_.lastIndexOf` which performs strict equality
   * comparisons of values, i.e. `===`.
   *
   * @private
   * @param {Array} array The array to inspect.
   * @param {*} value The value to search for.
   * @param {number} fromIndex The index to search from.
   * @returns {number} Returns the index of the matched value, else `-1`.
   */
  function strictLastIndexOf(array, value, fromIndex) {
    var index = fromIndex + 1;
    while (index--) {
      if (array[index] === value) {
        return index;
      }
    }
    return index;
  }

  /**
   * Gets the number of symbols in `string`.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {number} Returns the string size.
   */
  function stringSize(string) {
    return hasUnicode(string)
      ? unicodeSize(string)
      : asciiSize(string);
  }

  /**
   * Converts `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function stringToArray(string) {
    return hasUnicode(string)
      ? unicodeToArray(string)
      : asciiToArray(string);
  }

  /**
   * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
   * character of `string`.
   *
   * @private
   * @param {string} string The string to inspect.
   * @returns {number} Returns the index of the last non-whitespace character.
   */
  function trimmedEndIndex(string) {
    var index = string.length;

    while (index-- && reWhitespace.test(string.charAt(index))) {}
    return index;
  }

  /**
   * Used by `_.unescape` to convert HTML entities to characters.
   *
   * @private
   * @param {string} chr The matched character to unescape.
   * @returns {string} Returns the unescaped character.
   */
  var unescapeHtmlChar = basePropertyOf(htmlUnescapes);

  /**
   * Gets the size of a Unicode `string`.
   *
   * @private
   * @param {string} string The string inspect.
   * @returns {number} Returns the string size.
   */
  function unicodeSize(string) {
    var result = reUnicode.lastIndex = 0;
    while (reUnicode.test(string)) {
      ++result;
    }
    return result;
  }

  /**
   * Converts a Unicode `string` to an array.
   *
   * @private
   * @param {string} string The string to convert.
   * @returns {Array} Returns the converted array.
   */
  function unicodeToArray(string) {
    return string.match(reUnicode) || [];
  }

  /**
   * Splits a Unicode `string` into an array of its words.
   *
   * @private
   * @param {string} The string to inspect.
   * @returns {Array} Returns the words of `string`.
   */
  function unicodeWords(string) {
    return string.match(reUnicodeWord) || [];
  }

  /*--------------------------------------------------------------------------*/

  /**
   * Create a new pristine `lodash` function using the `context` object.
   *
   * @static
   * @memberOf _
   * @since 1.1.0
   * @category Util
   * @param {Object} [context=root] The context object.
   * @returns {Function} Returns a new `lodash` function.
   * @example
   *
   * _.mixin({ 'foo': _.constant('foo') });
   *
   * var lodash = _.runInContext();
   * lodash.mixin({ 'bar': lodash.constant('bar') });
   *
   * _.isFunction(_.foo);
   * // => true
   * _.isFunction(_.bar);
   * // => false
   *
   * lodash.isFunction(lodash.foo);
   * // => false
   * lodash.isFunction(lodash.bar);
   * // => true
   *
   * // Create a suped-up `defer` in Node.js.
   * var defer = _.runInContext({ 'setTimeout': setImmediate }).defer;
   */
  var runInContext = (function runInContext(context) {
    context = context == null ? root : _.defaults(root.Object(), context, _.pick(root, contextProps));

    /** Built-in constructor references. */
    var Array = context.Array,
        Date = context.Date,
        Error = context.Error,
        Function = context.Function,
        Math = context.Math,
        Object = context.Object,
        RegExp = context.RegExp,
        String = context.String,
        TypeError = context.TypeError;

    /** Used for built-in method references. */
    var arrayProto = Array.prototype,
        funcProto = Function.prototype,
        objectProto = Object.prototype;

    /** Used to detect overreaching core-js shims. */
    var coreJsData = context['__core-js_shared__'];

    /** Used to resolve the decompiled source of functions. */
    var funcToString = funcProto.toString;

    /** Used to check objects for own properties. */
    var hasOwnProperty = objectProto.hasOwnProperty;

    /** Used to generate unique IDs. */
    var idCounter = 0;

    /** Used to detect methods masquerading as native. */
    var maskSrcKey = (function() {
      var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
      return uid ? ('Symbol(src)_1.' + uid) : '';
    }());

    /**
     * Used to resolve the
     * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
     * of values.
     */
    var nativeObjectToString = objectProto.toString;

    /** Used to infer the `Object` constructor. */
    var objectCtorString = funcToString.call(Object);

    /** Used to restore the original `_` reference in `_.noConflict`. */
    var oldDash = root._;

    /** Used to detect if a method is native. */
    var reIsNative = RegExp('^' +
      funcToString.call(hasOwnProperty).replace(reRegExpChar, '\\$&')
      .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
    );

    /** Built-in value references. */
    var Buffer = moduleExports ? context.Buffer : undefined,
        Symbol = context.Symbol,
        Uint8Array = context.Uint8Array,
        allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined,
        getPrototype = overArg(Object.getPrototypeOf, Object),
        objectCreate = Object.create,
        propertyIsEnumerable = objectProto.propertyIsEnumerable,
        splice = arrayProto.splice,
        spreadableSymbol = Symbol ? Symbol.isConcatSpreadable : undefined,
        symIterator = Symbol ? Symbol.iterator : undefined,
        symToStringTag = Symbol ? Symbol.toStringTag : undefined;

    var defineProperty = (function() {
      try {
        var func = getNative(Object, 'defineProperty');
        func({}, '', {});
        return func;
      } catch (e) {}
    }());

    /** Mocked built-ins. */
    var ctxClearTimeout = context.clearTimeout !== root.clearTimeout && context.clearTimeout,
        ctxNow = Date && Date.now !== root.Date.now && Date.now,
        ctxSetTimeout = context.setTimeout !== root.setTimeout && context.setTimeout;

    /* Built-in method references for those with the same name as other `lodash` methods. */
    var nativeCeil = Math.ceil,
        nativeFloor = Math.floor,
        nativeGetSymbols = Object.getOwnPropertySymbols,
        nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined,
        nativeIsFinite = context.isFinite,
        nativeJoin = arrayProto.join,
        nativeKeys = overArg(Object.keys, Object),
        nativeMax = Math.max,
        nativeMin = Math.min,
        nativeNow = Date.now,
        nativeParseInt = context.parseInt,
        nativeRandom = Math.random,
        nativeReverse = arrayProto.reverse;

    /* Built-in method references that are verified to be native. */
    var DataView = getNative(context, 'DataView'),
        Map = getNative(context, 'Map'),
        Promise = getNative(context, 'Promise'),
        Set = getNative(context, 'Set'),
        WeakMap = getNative(context, 'WeakMap'),
        nativeCreate = getNative(Object, 'create');

    /** Used to store function metadata. */
    var metaMap = WeakMap && new WeakMap;

    /** Used to lookup unminified function names. */
    var realNames = {};

    /** Used to detect maps, sets, and weakmaps. */
    var dataViewCtorString = toSource(DataView),
        mapCtorString = toSource(Map),
        promiseCtorString = toSource(Promise),
        setCtorString = toSource(Set),
        weakMapCtorString = toSource(WeakMap);

    /** Used to convert symbols to primitives and strings. */
    var symbolProto = Symbol ? Symbol.prototype : undefined,
        symbolValueOf = symbolProto ? symbolProto.valueOf : undefined,
        symbolToString = symbolProto ? symbolProto.toString : undefined;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` object which wraps `value` to enable implicit method
     * chain sequences. Methods that operate on and return arrays, collections,
     * and functions can be chained together. Methods that retrieve a single value
     * or may return a primitive value will automatically end the chain sequence
     * and return the unwrapped value. Otherwise, the value must be unwrapped
     * with `_#value`.
     *
     * Explicit chain sequences, which must be unwrapped with `_#value`, may be
     * enabled using `_.chain`.
     *
     * The execution of chained methods is lazy, that is, it's deferred until
     * `_#value` is implicitly or explicitly called.
     *
     * Lazy evaluation allows several methods to support shortcut fusion.
     * Shortcut fusion is an optimization to merge iteratee calls; this avoids
     * the creation of intermediate arrays and can greatly reduce the number of
     * iteratee executions. Sections of a chain sequence qualify for shortcut
     * fusion if the section is applied to an array and iteratees accept only
     * one argument. The heuristic for whether a section qualifies for shortcut
     * fusion is subject to change.
     *
     * Chaining is supported in custom builds as long as the `_#value` method is
     * directly or indirectly included in the build.
     *
     * In addition to lodash methods, wrappers have `Array` and `String` methods.
     *
     * The wrapper `Array` methods are:
     * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
     *
     * The wrapper `String` methods are:
     * `replace` and `split`
     *
     * The wrapper methods that support shortcut fusion are:
     * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
     * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
     * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
     *
     * The chainable wrapper methods are:
     * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
     * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
     * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
     * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
     * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
     * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
     * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
     * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
     * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
     * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
     * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
     * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
     * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
     * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
     * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
     * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
     * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
     * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
     * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
     * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
     * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
     * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
     * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
     * `zipObject`, `zipObjectDeep`, and `zipWith`
     *
     * The wrapper methods that are **not** chainable by default are:
     * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
     * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
     * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
     * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
     * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
     * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
     * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
     * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
     * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
     * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
     * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
     * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
     * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
     * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
     * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
     * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
     * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
     * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
     * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
     * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
     * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
     * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
     * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
     * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
     * `upperFirst`, `value`, and `words`
     *
     * @name _
     * @constructor
     * @category Seq
     * @param {*} value The value to wrap in a `lodash` instance.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2, 3]);
     *
     * // Returns an unwrapped value.
     * wrapped.reduce(_.add);
     * // => 6
     *
     * // Returns a wrapped value.
     * var squares = wrapped.map(square);
     *
     * _.isArray(squares);
     * // => false
     *
     * _.isArray(squares.value());
     * // => true
     */
    function lodash(value) {
      if (isObjectLike(value) && !isArray(value) && !(value instanceof LazyWrapper)) {
        if (value instanceof LodashWrapper) {
          return value;
        }
        if (hasOwnProperty.call(value, '__wrapped__')) {
          return wrapperClone(value);
        }
      }
      return new LodashWrapper(value);
    }

    /**
     * The base implementation of `_.create` without support for assigning
     * properties to the created object.
     *
     * @private
     * @param {Object} proto The object to inherit from.
     * @returns {Object} Returns the new object.
     */
    var baseCreate = (function() {
      function object() {}
      return function(proto) {
        if (!isObject(proto)) {
          return {};
        }
        if (objectCreate) {
          return objectCreate(proto);
        }
        object.prototype = proto;
        var result = new object;
        object.prototype = undefined;
        return result;
      };
    }());

    /**
     * The function whose prototype chain sequence wrappers inherit from.
     *
     * @private
     */
    function baseLodash() {
      // No operation performed.
    }

    /**
     * The base constructor for creating `lodash` wrapper objects.
     *
     * @private
     * @param {*} value The value to wrap.
     * @param {boolean} [chainAll] Enable explicit method chain sequences.
     */
    function LodashWrapper(value, chainAll) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__chain__ = !!chainAll;
      this.__index__ = 0;
      this.__values__ = undefined;
    }

    /**
     * By default, the template delimiters used by lodash are like those in
     * embedded Ruby (ERB) as well as ES2015 template strings. Change the
     * following template settings to use alternative delimiters.
     *
     * @static
     * @memberOf _
     * @type {Object}
     */
    lodash.templateSettings = {

      /**
       * Used to detect `data` property values to be HTML-escaped.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'escape': reEscape,

      /**
       * Used to detect code to be evaluated.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'evaluate': reEvaluate,

      /**
       * Used to detect `data` property values to inject.
       *
       * @memberOf _.templateSettings
       * @type {RegExp}
       */
      'interpolate': reInterpolate,

      /**
       * Used to reference the data object in the template text.
       *
       * @memberOf _.templateSettings
       * @type {string}
       */
      'variable': '',

      /**
       * Used to import variables into the compiled template.
       *
       * @memberOf _.templateSettings
       * @type {Object}
       */
      'imports': {

        /**
         * A reference to the `lodash` function.
         *
         * @memberOf _.templateSettings.imports
         * @type {Function}
         */
        '_': lodash
      }
    };

    // Ensure wrappers are instances of `baseLodash`.
    lodash.prototype = baseLodash.prototype;
    lodash.prototype.constructor = lodash;

    LodashWrapper.prototype = baseCreate(baseLodash.prototype);
    LodashWrapper.prototype.constructor = LodashWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
     *
     * @private
     * @constructor
     * @param {*} value The value to wrap.
     */
    function LazyWrapper(value) {
      this.__wrapped__ = value;
      this.__actions__ = [];
      this.__dir__ = 1;
      this.__filtered__ = false;
      this.__iteratees__ = [];
      this.__takeCount__ = MAX_ARRAY_LENGTH;
      this.__views__ = [];
    }

    /**
     * Creates a clone of the lazy wrapper object.
     *
     * @private
     * @name clone
     * @memberOf LazyWrapper
     * @returns {Object} Returns the cloned `LazyWrapper` object.
     */
    function lazyClone() {
      var result = new LazyWrapper(this.__wrapped__);
      result.__actions__ = copyArray(this.__actions__);
      result.__dir__ = this.__dir__;
      result.__filtered__ = this.__filtered__;
      result.__iteratees__ = copyArray(this.__iteratees__);
      result.__takeCount__ = this.__takeCount__;
      result.__views__ = copyArray(this.__views__);
      return result;
    }

    /**
     * Reverses the direction of lazy iteration.
     *
     * @private
     * @name reverse
     * @memberOf LazyWrapper
     * @returns {Object} Returns the new reversed `LazyWrapper` object.
     */
    function lazyReverse() {
      if (this.__filtered__) {
        var result = new LazyWrapper(this);
        result.__dir__ = -1;
        result.__filtered__ = true;
      } else {
        result = this.clone();
        result.__dir__ *= -1;
      }
      return result;
    }

    /**
     * Extracts the unwrapped value from its lazy wrapper.
     *
     * @private
     * @name value
     * @memberOf LazyWrapper
     * @returns {*} Returns the unwrapped value.
     */
    function lazyValue() {
      var array = this.__wrapped__.value(),
          dir = this.__dir__,
          isArr = isArray(array),
          isRight = dir < 0,
          arrLength = isArr ? array.length : 0,
          view = getView(0, arrLength, this.__views__),
          start = view.start,
          end = view.end,
          length = end - start,
          index = isRight ? end : (start - 1),
          iteratees = this.__iteratees__,
          iterLength = iteratees.length,
          resIndex = 0,
          takeCount = nativeMin(length, this.__takeCount__);

      if (!isArr || (!isRight && arrLength == length && takeCount == length)) {
        return baseWrapperValue(array, this.__actions__);
      }
      var result = [];

      outer:
      while (length-- && resIndex < takeCount) {
        index += dir;

        var iterIndex = -1,
            value = array[index];

        while (++iterIndex < iterLength) {
          var data = iteratees[iterIndex],
              iteratee = data.iteratee,
              type = data.type,
              computed = iteratee(value);

          if (type == LAZY_MAP_FLAG) {
            value = computed;
          } else if (!computed) {
            if (type == LAZY_FILTER_FLAG) {
              continue outer;
            } else {
              break outer;
            }
          }
        }
        result[resIndex++] = value;
      }
      return result;
    }

    // Ensure `LazyWrapper` is an instance of `baseLodash`.
    LazyWrapper.prototype = baseCreate(baseLodash.prototype);
    LazyWrapper.prototype.constructor = LazyWrapper;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a hash object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Hash(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the hash.
     *
     * @private
     * @name clear
     * @memberOf Hash
     */
    function hashClear() {
      this.__data__ = nativeCreate ? nativeCreate(null) : {};
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the hash.
     *
     * @private
     * @name delete
     * @memberOf Hash
     * @param {Object} hash The hash to modify.
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function hashDelete(key) {
      var result = this.has(key) && delete this.__data__[key];
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the hash value for `key`.
     *
     * @private
     * @name get
     * @memberOf Hash
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function hashGet(key) {
      var data = this.__data__;
      if (nativeCreate) {
        var result = data[key];
        return result === HASH_UNDEFINED ? undefined : result;
      }
      return hasOwnProperty.call(data, key) ? data[key] : undefined;
    }

    /**
     * Checks if a hash value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Hash
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function hashHas(key) {
      var data = this.__data__;
      return nativeCreate ? (data[key] !== undefined) : hasOwnProperty.call(data, key);
    }

    /**
     * Sets the hash `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Hash
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the hash instance.
     */
    function hashSet(key, value) {
      var data = this.__data__;
      this.size += this.has(key) ? 0 : 1;
      data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
      return this;
    }

    // Add methods to `Hash`.
    Hash.prototype.clear = hashClear;
    Hash.prototype['delete'] = hashDelete;
    Hash.prototype.get = hashGet;
    Hash.prototype.has = hashHas;
    Hash.prototype.set = hashSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates an list cache object.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function ListCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the list cache.
     *
     * @private
     * @name clear
     * @memberOf ListCache
     */
    function listCacheClear() {
      this.__data__ = [];
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the list cache.
     *
     * @private
     * @name delete
     * @memberOf ListCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function listCacheDelete(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        return false;
      }
      var lastIndex = data.length - 1;
      if (index == lastIndex) {
        data.pop();
      } else {
        splice.call(data, index, 1);
      }
      --this.size;
      return true;
    }

    /**
     * Gets the list cache value for `key`.
     *
     * @private
     * @name get
     * @memberOf ListCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function listCacheGet(key) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      return index < 0 ? undefined : data[index][1];
    }

    /**
     * Checks if a list cache value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf ListCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function listCacheHas(key) {
      return assocIndexOf(this.__data__, key) > -1;
    }

    /**
     * Sets the list cache `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf ListCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the list cache instance.
     */
    function listCacheSet(key, value) {
      var data = this.__data__,
          index = assocIndexOf(data, key);

      if (index < 0) {
        ++this.size;
        data.push([key, value]);
      } else {
        data[index][1] = value;
      }
      return this;
    }

    // Add methods to `ListCache`.
    ListCache.prototype.clear = listCacheClear;
    ListCache.prototype['delete'] = listCacheDelete;
    ListCache.prototype.get = listCacheGet;
    ListCache.prototype.has = listCacheHas;
    ListCache.prototype.set = listCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a map cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function MapCache(entries) {
      var index = -1,
          length = entries == null ? 0 : entries.length;

      this.clear();
      while (++index < length) {
        var entry = entries[index];
        this.set(entry[0], entry[1]);
      }
    }

    /**
     * Removes all key-value entries from the map.
     *
     * @private
     * @name clear
     * @memberOf MapCache
     */
    function mapCacheClear() {
      this.size = 0;
      this.__data__ = {
        'hash': new Hash,
        'map': new (Map || ListCache),
        'string': new Hash
      };
    }

    /**
     * Removes `key` and its value from the map.
     *
     * @private
     * @name delete
     * @memberOf MapCache
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function mapCacheDelete(key) {
      var result = getMapData(this, key)['delete'](key);
      this.size -= result ? 1 : 0;
      return result;
    }

    /**
     * Gets the map value for `key`.
     *
     * @private
     * @name get
     * @memberOf MapCache
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function mapCacheGet(key) {
      return getMapData(this, key).get(key);
    }

    /**
     * Checks if a map value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf MapCache
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function mapCacheHas(key) {
      return getMapData(this, key).has(key);
    }

    /**
     * Sets the map `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf MapCache
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the map cache instance.
     */
    function mapCacheSet(key, value) {
      var data = getMapData(this, key),
          size = data.size;

      data.set(key, value);
      this.size += data.size == size ? 0 : 1;
      return this;
    }

    // Add methods to `MapCache`.
    MapCache.prototype.clear = mapCacheClear;
    MapCache.prototype['delete'] = mapCacheDelete;
    MapCache.prototype.get = mapCacheGet;
    MapCache.prototype.has = mapCacheHas;
    MapCache.prototype.set = mapCacheSet;

    /*------------------------------------------------------------------------*/

    /**
     *
     * Creates an array cache object to store unique values.
     *
     * @private
     * @constructor
     * @param {Array} [values] The values to cache.
     */
    function SetCache(values) {
      var index = -1,
          length = values == null ? 0 : values.length;

      this.__data__ = new MapCache;
      while (++index < length) {
        this.add(values[index]);
      }
    }

    /**
     * Adds `value` to the array cache.
     *
     * @private
     * @name add
     * @memberOf SetCache
     * @alias push
     * @param {*} value The value to cache.
     * @returns {Object} Returns the cache instance.
     */
    function setCacheAdd(value) {
      this.__data__.set(value, HASH_UNDEFINED);
      return this;
    }

    /**
     * Checks if `value` is in the array cache.
     *
     * @private
     * @name has
     * @memberOf SetCache
     * @param {*} value The value to search for.
     * @returns {boolean} Returns `true` if `value` is found, else `false`.
     */
    function setCacheHas(value) {
      return this.__data__.has(value);
    }

    // Add methods to `SetCache`.
    SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
    SetCache.prototype.has = setCacheHas;

    /*------------------------------------------------------------------------*/

    /**
     * Creates a stack cache object to store key-value pairs.
     *
     * @private
     * @constructor
     * @param {Array} [entries] The key-value pairs to cache.
     */
    function Stack(entries) {
      var data = this.__data__ = new ListCache(entries);
      this.size = data.size;
    }

    /**
     * Removes all key-value entries from the stack.
     *
     * @private
     * @name clear
     * @memberOf Stack
     */
    function stackClear() {
      this.__data__ = new ListCache;
      this.size = 0;
    }

    /**
     * Removes `key` and its value from the stack.
     *
     * @private
     * @name delete
     * @memberOf Stack
     * @param {string} key The key of the value to remove.
     * @returns {boolean} Returns `true` if the entry was removed, else `false`.
     */
    function stackDelete(key) {
      var data = this.__data__,
          result = data['delete'](key);

      this.size = data.size;
      return result;
    }

    /**
     * Gets the stack value for `key`.
     *
     * @private
     * @name get
     * @memberOf Stack
     * @param {string} key The key of the value to get.
     * @returns {*} Returns the entry value.
     */
    function stackGet(key) {
      return this.__data__.get(key);
    }

    /**
     * Checks if a stack value for `key` exists.
     *
     * @private
     * @name has
     * @memberOf Stack
     * @param {string} key The key of the entry to check.
     * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
     */
    function stackHas(key) {
      return this.__data__.has(key);
    }

    /**
     * Sets the stack `key` to `value`.
     *
     * @private
     * @name set
     * @memberOf Stack
     * @param {string} key The key of the value to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns the stack cache instance.
     */
    function stackSet(key, value) {
      var data = this.__data__;
      if (data instanceof ListCache) {
        var pairs = data.__data__;
        if (!Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
          pairs.push([key, value]);
          this.size = ++data.size;
          return this;
        }
        data = this.__data__ = new MapCache(pairs);
      }
      data.set(key, value);
      this.size = data.size;
      return this;
    }

    // Add methods to `Stack`.
    Stack.prototype.clear = stackClear;
    Stack.prototype['delete'] = stackDelete;
    Stack.prototype.get = stackGet;
    Stack.prototype.has = stackHas;
    Stack.prototype.set = stackSet;

    /*------------------------------------------------------------------------*/

    /**
     * Creates an array of the enumerable property names of the array-like `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @param {boolean} inherited Specify returning inherited property names.
     * @returns {Array} Returns the array of property names.
     */
    function arrayLikeKeys(value, inherited) {
      var isArr = isArray(value),
          isArg = !isArr && isArguments(value),
          isBuff = !isArr && !isArg && isBuffer(value),
          isType = !isArr && !isArg && !isBuff && isTypedArray(value),
          skipIndexes = isArr || isArg || isBuff || isType,
          result = skipIndexes ? baseTimes(value.length, String) : [],
          length = result.length;

      for (var key in value) {
        if ((inherited || hasOwnProperty.call(value, key)) &&
            !(skipIndexes && (
               // Safari 9 has enumerable `arguments.length` in strict mode.
               key == 'length' ||
               // Node.js 0.10 has enumerable non-index properties on buffers.
               (isBuff && (key == 'offset' || key == 'parent')) ||
               // PhantomJS 2 has enumerable non-index properties on typed arrays.
               (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
               // Skip index properties.
               isIndex(key, length)
            ))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * A specialized version of `_.sample` for arrays.
     *
     * @private
     * @param {Array} array The array to sample.
     * @returns {*} Returns the random element.
     */
    function arraySample(array) {
      var length = array.length;
      return length ? array[baseRandom(0, length - 1)] : undefined;
    }

    /**
     * A specialized version of `_.sampleSize` for arrays.
     *
     * @private
     * @param {Array} array The array to sample.
     * @param {number} n The number of elements to sample.
     * @returns {Array} Returns the random elements.
     */
    function arraySampleSize(array, n) {
      return shuffleSelf(copyArray(array), baseClamp(n, 0, array.length));
    }

    /**
     * A specialized version of `_.shuffle` for arrays.
     *
     * @private
     * @param {Array} array The array to shuffle.
     * @returns {Array} Returns the new shuffled array.
     */
    function arrayShuffle(array) {
      return shuffleSelf(copyArray(array));
    }

    /**
     * This function is like `assignValue` except that it doesn't assign
     * `undefined` values.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignMergeValue(object, key, value) {
      if ((value !== undefined && !eq(object[key], value)) ||
          (value === undefined && !(key in object))) {
        baseAssignValue(object, key, value);
      }
    }

    /**
     * Assigns `value` to `key` of `object` if the existing value is not equivalent
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function assignValue(object, key, value) {
      var objValue = object[key];
      if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
          (value === undefined && !(key in object))) {
        baseAssignValue(object, key, value);
      }
    }

    /**
     * Gets the index at which the `key` is found in `array` of key-value pairs.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {*} key The key to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     */
    function assocIndexOf(array, key) {
      var length = array.length;
      while (length--) {
        if (eq(array[length][0], key)) {
          return length;
        }
      }
      return -1;
    }

    /**
     * Aggregates elements of `collection` on `accumulator` with keys transformed
     * by `iteratee` and values set by `setter`.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform keys.
     * @param {Object} accumulator The initial aggregated object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseAggregator(collection, setter, iteratee, accumulator) {
      baseEach(collection, function(value, key, collection) {
        setter(accumulator, value, iteratee(value), collection);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.assign` without support for multiple sources
     * or `customizer` functions.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @returns {Object} Returns `object`.
     */
    function baseAssign(object, source) {
      return object && copyObject(source, keys(source), object);
    }

    /**
     * The base implementation of `_.assignIn` without support for multiple sources
     * or `customizer` functions.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @returns {Object} Returns `object`.
     */
    function baseAssignIn(object, source) {
      return object && copyObject(source, keysIn(source), object);
    }

    /**
     * The base implementation of `assignValue` and `assignMergeValue` without
     * value checks.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {string} key The key of the property to assign.
     * @param {*} value The value to assign.
     */
    function baseAssignValue(object, key, value) {
      if (key == '__proto__' && defineProperty) {
        defineProperty(object, key, {
          'configurable': true,
          'enumerable': true,
          'value': value,
          'writable': true
        });
      } else {
        object[key] = value;
      }
    }

    /**
     * The base implementation of `_.at` without support for individual paths.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {string[]} paths The property paths to pick.
     * @returns {Array} Returns the picked elements.
     */
    function baseAt(object, paths) {
      var index = -1,
          length = paths.length,
          result = Array(length),
          skip = object == null;

      while (++index < length) {
        result[index] = skip ? undefined : get(object, paths[index]);
      }
      return result;
    }

    /**
     * The base implementation of `_.clamp` which doesn't coerce arguments.
     *
     * @private
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     */
    function baseClamp(number, lower, upper) {
      if (number === number) {
        if (upper !== undefined) {
          number = number <= upper ? number : upper;
        }
        if (lower !== undefined) {
          number = number >= lower ? number : lower;
        }
      }
      return number;
    }

    /**
     * The base implementation of `_.clone` and `_.cloneDeep` which tracks
     * traversed objects.
     *
     * @private
     * @param {*} value The value to clone.
     * @param {boolean} bitmask The bitmask flags.
     *  1 - Deep clone
     *  2 - Flatten inherited properties
     *  4 - Clone symbols
     * @param {Function} [customizer] The function to customize cloning.
     * @param {string} [key] The key of `value`.
     * @param {Object} [object] The parent object of `value`.
     * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
     * @returns {*} Returns the cloned value.
     */
    function baseClone(value, bitmask, customizer, key, object, stack) {
      var result,
          isDeep = bitmask & CLONE_DEEP_FLAG,
          isFlat = bitmask & CLONE_FLAT_FLAG,
          isFull = bitmask & CLONE_SYMBOLS_FLAG;

      if (customizer) {
        result = object ? customizer(value, key, object, stack) : customizer(value);
      }
      if (result !== undefined) {
        return result;
      }
      if (!isObject(value)) {
        return value;
      }
      var isArr = isArray(value);
      if (isArr) {
        result = initCloneArray(value);
        if (!isDeep) {
          return copyArray(value, result);
        }
      } else {
        var tag = getTag(value),
            isFunc = tag == funcTag || tag == genTag;

        if (isBuffer(value)) {
          return cloneBuffer(value, isDeep);
        }
        if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
          result = (isFlat || isFunc) ? {} : initCloneObject(value);
          if (!isDeep) {
            return isFlat
              ? copySymbolsIn(value, baseAssignIn(result, value))
              : copySymbols(value, baseAssign(result, value));
          }
        } else {
          if (!cloneableTags[tag]) {
            return object ? value : {};
          }
          result = initCloneByTag(value, tag, isDeep);
        }
      }
      // Check for circular references and return its corresponding clone.
      stack || (stack = new Stack);
      var stacked = stack.get(value);
      if (stacked) {
        return stacked;
      }
      stack.set(value, result);

      if (isSet(value)) {
        value.forEach(function(subValue) {
          result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
        });
      } else if (isMap(value)) {
        value.forEach(function(subValue, key) {
          result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
        });
      }

      var keysFunc = isFull
        ? (isFlat ? getAllKeysIn : getAllKeys)
        : (isFlat ? keysIn : keys);

      var props = isArr ? undefined : keysFunc(value);
      arrayEach(props || value, function(subValue, key) {
        if (props) {
          key = subValue;
          subValue = value[key];
        }
        // Recursively populate clone (susceptible to call stack limits).
        assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
      });
      return result;
    }

    /**
     * The base implementation of `_.conforms` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     */
    function baseConforms(source) {
      var props = keys(source);
      return function(object) {
        return baseConformsTo(object, source, props);
      };
    }

    /**
     * The base implementation of `_.conformsTo` which accepts `props` to check.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property predicates to conform to.
     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
     */
    function baseConformsTo(object, source, props) {
      var length = props.length;
      if (object == null) {
        return !length;
      }
      object = Object(object);
      while (length--) {
        var key = props[length],
            predicate = source[key],
            value = object[key];

        if ((value === undefined && !(key in object)) || !predicate(value)) {
          return false;
        }
      }
      return true;
    }

    /**
     * The base implementation of `_.delay` and `_.defer` which accepts `args`
     * to provide to `func`.
     *
     * @private
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {Array} args The arguments to provide to `func`.
     * @returns {number|Object} Returns the timer id or timeout object.
     */
    function baseDelay(func, wait, args) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return setTimeout(function() { func.apply(undefined, args); }, wait);
    }

    /**
     * The base implementation of methods like `_.difference` without support
     * for excluding multiple arrays or iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Array} values The values to exclude.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     */
    function baseDifference(array, values, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          isCommon = true,
          length = array.length,
          result = [],
          valuesLength = values.length;

      if (!length) {
        return result;
      }
      if (iteratee) {
        values = arrayMap(values, baseUnary(iteratee));
      }
      if (comparator) {
        includes = arrayIncludesWith;
        isCommon = false;
      }
      else if (values.length >= LARGE_ARRAY_SIZE) {
        includes = cacheHas;
        isCommon = false;
        values = new SetCache(values);
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee == null ? value : iteratee(value);

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var valuesIndex = valuesLength;
          while (valuesIndex--) {
            if (values[valuesIndex] === computed) {
              continue outer;
            }
          }
          result.push(value);
        }
        else if (!includes(values, computed, comparator)) {
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.forEach` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEach = createBaseEach(baseForOwn);

    /**
     * The base implementation of `_.forEachRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     */
    var baseEachRight = createBaseEach(baseForOwnRight, true);

    /**
     * The base implementation of `_.every` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`
     */
    function baseEvery(collection, predicate) {
      var result = true;
      baseEach(collection, function(value, index, collection) {
        result = !!predicate(value, index, collection);
        return result;
      });
      return result;
    }

    /**
     * The base implementation of methods like `_.max` and `_.min` which accepts a
     * `comparator` to determine the extremum value.
     *
     * @private
     * @param {Array} array The array to iterate over.
     * @param {Function} iteratee The iteratee invoked per iteration.
     * @param {Function} comparator The comparator used to compare values.
     * @returns {*} Returns the extremum value.
     */
    function baseExtremum(array, iteratee, comparator) {
      var index = -1,
          length = array.length;

      while (++index < length) {
        var value = array[index],
            current = iteratee(value);

        if (current != null && (computed === undefined
              ? (current === current && !isSymbol(current))
              : comparator(current, computed)
            )) {
          var computed = current,
              result = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.fill` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     */
    function baseFill(array, value, start, end) {
      var length = array.length;

      start = toInteger(start);
      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = (end === undefined || end > length) ? length : toInteger(end);
      if (end < 0) {
        end += length;
      }
      end = start > end ? 0 : toLength(end);
      while (start < end) {
        array[start++] = value;
      }
      return array;
    }

    /**
     * The base implementation of `_.filter` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     */
    function baseFilter(collection, predicate) {
      var result = [];
      baseEach(collection, function(value, index, collection) {
        if (predicate(value, index, collection)) {
          result.push(value);
        }
      });
      return result;
    }

    /**
     * The base implementation of `_.flatten` with support for restricting flattening.
     *
     * @private
     * @param {Array} array The array to flatten.
     * @param {number} depth The maximum recursion depth.
     * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
     * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
     * @param {Array} [result=[]] The initial result value.
     * @returns {Array} Returns the new flattened array.
     */
    function baseFlatten(array, depth, predicate, isStrict, result) {
      var index = -1,
          length = array.length;

      predicate || (predicate = isFlattenable);
      result || (result = []);

      while (++index < length) {
        var value = array[index];
        if (depth > 0 && predicate(value)) {
          if (depth > 1) {
            // Recursively flatten arrays (susceptible to call stack limits).
            baseFlatten(value, depth - 1, predicate, isStrict, result);
          } else {
            arrayPush(result, value);
          }
        } else if (!isStrict) {
          result[result.length] = value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `baseForOwn` which iterates over `object`
     * properties returned by `keysFunc` and invokes `iteratee` for each property.
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseFor = createBaseFor();

    /**
     * This function is like `baseFor` except that it iterates over properties
     * in the opposite order.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @returns {Object} Returns `object`.
     */
    var baseForRight = createBaseFor(true);

    /**
     * The base implementation of `_.forOwn` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwn(object, iteratee) {
      return object && baseFor(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.forOwnRight` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Object} Returns `object`.
     */
    function baseForOwnRight(object, iteratee) {
      return object && baseForRight(object, iteratee, keys);
    }

    /**
     * The base implementation of `_.functions` which creates an array of
     * `object` function property names filtered from `props`.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Array} props The property names to filter.
     * @returns {Array} Returns the function names.
     */
    function baseFunctions(object, props) {
      return arrayFilter(props, function(key) {
        return isFunction(object[key]);
      });
    }

    /**
     * The base implementation of `_.get` without support for default values.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @returns {*} Returns the resolved value.
     */
    function baseGet(object, path) {
      path = castPath(path, object);

      var index = 0,
          length = path.length;

      while (object != null && index < length) {
        object = object[toKey(path[index++])];
      }
      return (index && index == length) ? object : undefined;
    }

    /**
     * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
     * `keysFunc` and `symbolsFunc` to get the enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Function} keysFunc The function to get the keys of `object`.
     * @param {Function} symbolsFunc The function to get the symbols of `object`.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function baseGetAllKeys(object, keysFunc, symbolsFunc) {
      var result = keysFunc(object);
      return isArray(object) ? result : arrayPush(result, symbolsFunc(object));
    }

    /**
     * The base implementation of `getTag` without fallbacks for buggy environments.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    function baseGetTag(value) {
      if (value == null) {
        return value === undefined ? undefinedTag : nullTag;
      }
      return (symToStringTag && symToStringTag in Object(value))
        ? getRawTag(value)
        : objectToString(value);
    }

    /**
     * The base implementation of `_.gt` which doesn't coerce arguments.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     */
    function baseGt(value, other) {
      return value > other;
    }

    /**
     * The base implementation of `_.has` without support for deep paths.
     *
     * @private
     * @param {Object} [object] The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHas(object, key) {
      return object != null && hasOwnProperty.call(object, key);
    }

    /**
     * The base implementation of `_.hasIn` without support for deep paths.
     *
     * @private
     * @param {Object} [object] The object to query.
     * @param {Array|string} key The key to check.
     * @returns {boolean} Returns `true` if `key` exists, else `false`.
     */
    function baseHasIn(object, key) {
      return object != null && key in Object(object);
    }

    /**
     * The base implementation of `_.inRange` which doesn't coerce arguments.
     *
     * @private
     * @param {number} number The number to check.
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     */
    function baseInRange(number, start, end) {
      return number >= nativeMin(start, end) && number < nativeMax(start, end);
    }

    /**
     * The base implementation of methods like `_.intersection`, without support
     * for iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of shared values.
     */
    function baseIntersection(arrays, iteratee, comparator) {
      var includes = comparator ? arrayIncludesWith : arrayIncludes,
          length = arrays[0].length,
          othLength = arrays.length,
          othIndex = othLength,
          caches = Array(othLength),
          maxLength = Infinity,
          result = [];

      while (othIndex--) {
        var array = arrays[othIndex];
        if (othIndex && iteratee) {
          array = arrayMap(array, baseUnary(iteratee));
        }
        maxLength = nativeMin(array.length, maxLength);
        caches[othIndex] = !comparator && (iteratee || (length >= 120 && array.length >= 120))
          ? new SetCache(othIndex && array)
          : undefined;
      }
      array = arrays[0];

      var index = -1,
          seen = caches[0];

      outer:
      while (++index < length && result.length < maxLength) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (!(seen
              ? cacheHas(seen, computed)
              : includes(result, computed, comparator)
            )) {
          othIndex = othLength;
          while (--othIndex) {
            var cache = caches[othIndex];
            if (!(cache
                  ? cacheHas(cache, computed)
                  : includes(arrays[othIndex], computed, comparator))
                ) {
              continue outer;
            }
          }
          if (seen) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.invert` and `_.invertBy` which inverts
     * `object` with values transformed by `iteratee` and set by `setter`.
     *
     * @private
     * @param {Object} object The object to iterate over.
     * @param {Function} setter The function to set `accumulator` values.
     * @param {Function} iteratee The iteratee to transform values.
     * @param {Object} accumulator The initial inverted object.
     * @returns {Function} Returns `accumulator`.
     */
    function baseInverter(object, setter, iteratee, accumulator) {
      baseForOwn(object, function(value, key, object) {
        setter(accumulator, iteratee(value), key, object);
      });
      return accumulator;
    }

    /**
     * The base implementation of `_.invoke` without support for individual
     * method arguments.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {Array} args The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     */
    function baseInvoke(object, path, args) {
      path = castPath(path, object);
      object = parent(object, path);
      var func = object == null ? object : object[toKey(last(path))];
      return func == null ? undefined : apply(func, object, args);
    }

    /**
     * The base implementation of `_.isArguments`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     */
    function baseIsArguments(value) {
      return isObjectLike(value) && baseGetTag(value) == argsTag;
    }

    /**
     * The base implementation of `_.isArrayBuffer` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
     */
    function baseIsArrayBuffer(value) {
      return isObjectLike(value) && baseGetTag(value) == arrayBufferTag;
    }

    /**
     * The base implementation of `_.isDate` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
     */
    function baseIsDate(value) {
      return isObjectLike(value) && baseGetTag(value) == dateTag;
    }

    /**
     * The base implementation of `_.isEqual` which supports partial comparisons
     * and tracks traversed objects.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {boolean} bitmask The bitmask flags.
     *  1 - Unordered comparison
     *  2 - Partial comparison
     * @param {Function} [customizer] The function to customize comparisons.
     * @param {Object} [stack] Tracks traversed `value` and `other` objects.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     */
    function baseIsEqual(value, other, bitmask, customizer, stack) {
      if (value === other) {
        return true;
      }
      if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
        return value !== value && other !== other;
      }
      return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
    }

    /**
     * A specialized version of `baseIsEqual` for arrays and objects which performs
     * deep comparisons and tracks traversed objects enabling objects with circular
     * references to be compared.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} [stack] Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
      var objIsArr = isArray(object),
          othIsArr = isArray(other),
          objTag = objIsArr ? arrayTag : getTag(object),
          othTag = othIsArr ? arrayTag : getTag(other);

      objTag = objTag == argsTag ? objectTag : objTag;
      othTag = othTag == argsTag ? objectTag : othTag;

      var objIsObj = objTag == objectTag,
          othIsObj = othTag == objectTag,
          isSameTag = objTag == othTag;

      if (isSameTag && isBuffer(object)) {
        if (!isBuffer(other)) {
          return false;
        }
        objIsArr = true;
        objIsObj = false;
      }
      if (isSameTag && !objIsObj) {
        stack || (stack = new Stack);
        return (objIsArr || isTypedArray(object))
          ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
          : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
      }
      if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
        var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
            othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

        if (objIsWrapped || othIsWrapped) {
          var objUnwrapped = objIsWrapped ? object.value() : object,
              othUnwrapped = othIsWrapped ? other.value() : other;

          stack || (stack = new Stack);
          return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
        }
      }
      if (!isSameTag) {
        return false;
      }
      stack || (stack = new Stack);
      return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
    }

    /**
     * The base implementation of `_.isMap` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
     */
    function baseIsMap(value) {
      return isObjectLike(value) && getTag(value) == mapTag;
    }

    /**
     * The base implementation of `_.isMatch` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Array} matchData The property names, values, and compare flags to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     */
    function baseIsMatch(object, source, matchData, customizer) {
      var index = matchData.length,
          length = index,
          noCustomizer = !customizer;

      if (object == null) {
        return !length;
      }
      object = Object(object);
      while (index--) {
        var data = matchData[index];
        if ((noCustomizer && data[2])
              ? data[1] !== object[data[0]]
              : !(data[0] in object)
            ) {
          return false;
        }
      }
      while (++index < length) {
        data = matchData[index];
        var key = data[0],
            objValue = object[key],
            srcValue = data[1];

        if (noCustomizer && data[2]) {
          if (objValue === undefined && !(key in object)) {
            return false;
          }
        } else {
          var stack = new Stack;
          if (customizer) {
            var result = customizer(objValue, srcValue, key, object, source, stack);
          }
          if (!(result === undefined
                ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG, customizer, stack)
                : result
              )) {
            return false;
          }
        }
      }
      return true;
    }

    /**
     * The base implementation of `_.isNative` without bad shim checks.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     */
    function baseIsNative(value) {
      if (!isObject(value) || isMasked(value)) {
        return false;
      }
      var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
      return pattern.test(toSource(value));
    }

    /**
     * The base implementation of `_.isRegExp` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
     */
    function baseIsRegExp(value) {
      return isObjectLike(value) && baseGetTag(value) == regexpTag;
    }

    /**
     * The base implementation of `_.isSet` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
     */
    function baseIsSet(value) {
      return isObjectLike(value) && getTag(value) == setTag;
    }

    /**
     * The base implementation of `_.isTypedArray` without Node.js optimizations.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     */
    function baseIsTypedArray(value) {
      return isObjectLike(value) &&
        isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
    }

    /**
     * The base implementation of `_.iteratee`.
     *
     * @private
     * @param {*} [value=_.identity] The value to convert to an iteratee.
     * @returns {Function} Returns the iteratee.
     */
    function baseIteratee(value) {
      // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
      // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
      if (typeof value == 'function') {
        return value;
      }
      if (value == null) {
        return identity;
      }
      if (typeof value == 'object') {
        return isArray(value)
          ? baseMatchesProperty(value[0], value[1])
          : baseMatches(value);
      }
      return property(value);
    }

    /**
     * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeys(object) {
      if (!isPrototype(object)) {
        return nativeKeys(object);
      }
      var result = [];
      for (var key in Object(object)) {
        if (hasOwnProperty.call(object, key) && key != 'constructor') {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function baseKeysIn(object) {
      if (!isObject(object)) {
        return nativeKeysIn(object);
      }
      var isProto = isPrototype(object),
          result = [];

      for (var key in object) {
        if (!(key == 'constructor' && (isProto || !hasOwnProperty.call(object, key)))) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.lt` which doesn't coerce arguments.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     */
    function baseLt(value, other) {
      return value < other;
    }

    /**
     * The base implementation of `_.map` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} iteratee The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     */
    function baseMap(collection, iteratee) {
      var index = -1,
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value, key, collection) {
        result[++index] = iteratee(value, key, collection);
      });
      return result;
    }

    /**
     * The base implementation of `_.matches` which doesn't clone `source`.
     *
     * @private
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatches(source) {
      var matchData = getMatchData(source);
      if (matchData.length == 1 && matchData[0][2]) {
        return matchesStrictComparable(matchData[0][0], matchData[0][1]);
      }
      return function(object) {
        return object === source || baseIsMatch(object, source, matchData);
      };
    }

    /**
     * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
     *
     * @private
     * @param {string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function baseMatchesProperty(path, srcValue) {
      if (isKey(path) && isStrictComparable(srcValue)) {
        return matchesStrictComparable(toKey(path), srcValue);
      }
      return function(object) {
        var objValue = get(object, path);
        return (objValue === undefined && objValue === srcValue)
          ? hasIn(object, path)
          : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
      };
    }

    /**
     * The base implementation of `_.merge` without support for multiple sources.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} [customizer] The function to customize merged values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMerge(object, source, srcIndex, customizer, stack) {
      if (object === source) {
        return;
      }
      baseFor(source, function(srcValue, key) {
        stack || (stack = new Stack);
        if (isObject(srcValue)) {
          baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
        }
        else {
          var newValue = customizer
            ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
            : undefined;

          if (newValue === undefined) {
            newValue = srcValue;
          }
          assignMergeValue(object, key, newValue);
        }
      }, keysIn);
    }

    /**
     * A specialized version of `baseMerge` for arrays and objects which performs
     * deep merges and tracks traversed objects enabling objects with circular
     * references to be merged.
     *
     * @private
     * @param {Object} object The destination object.
     * @param {Object} source The source object.
     * @param {string} key The key of the value to merge.
     * @param {number} srcIndex The index of `source`.
     * @param {Function} mergeFunc The function to merge values.
     * @param {Function} [customizer] The function to customize assigned values.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     */
    function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
      var objValue = safeGet(object, key),
          srcValue = safeGet(source, key),
          stacked = stack.get(srcValue);

      if (stacked) {
        assignMergeValue(object, key, stacked);
        return;
      }
      var newValue = customizer
        ? customizer(objValue, srcValue, (key + ''), object, source, stack)
        : undefined;

      var isCommon = newValue === undefined;

      if (isCommon) {
        var isArr = isArray(srcValue),
            isBuff = !isArr && isBuffer(srcValue),
            isTyped = !isArr && !isBuff && isTypedArray(srcValue);

        newValue = srcValue;
        if (isArr || isBuff || isTyped) {
          if (isArray(objValue)) {
            newValue = objValue;
          }
          else if (isArrayLikeObject(objValue)) {
            newValue = copyArray(objValue);
          }
          else if (isBuff) {
            isCommon = false;
            newValue = cloneBuffer(srcValue, true);
          }
          else if (isTyped) {
            isCommon = false;
            newValue = cloneTypedArray(srcValue, true);
          }
          else {
            newValue = [];
          }
        }
        else if (isPlainObject(srcValue) || isArguments(srcValue)) {
          newValue = objValue;
          if (isArguments(objValue)) {
            newValue = toPlainObject(objValue);
          }
          else if (!isObject(objValue) || isFunction(objValue)) {
            newValue = initCloneObject(srcValue);
          }
        }
        else {
          isCommon = false;
        }
      }
      if (isCommon) {
        // Recursively merge objects and arrays (susceptible to call stack limits).
        stack.set(srcValue, newValue);
        mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
        stack['delete'](srcValue);
      }
      assignMergeValue(object, key, newValue);
    }

    /**
     * The base implementation of `_.nth` which doesn't coerce arguments.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {number} n The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     */
    function baseNth(array, n) {
      var length = array.length;
      if (!length) {
        return;
      }
      n += n < 0 ? length : 0;
      return isIndex(n, length) ? array[n] : undefined;
    }

    /**
     * The base implementation of `_.orderBy` without param guards.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
     * @param {string[]} orders The sort orders of `iteratees`.
     * @returns {Array} Returns the new sorted array.
     */
    function baseOrderBy(collection, iteratees, orders) {
      if (iteratees.length) {
        iteratees = arrayMap(iteratees, function(iteratee) {
          if (isArray(iteratee)) {
            return function(value) {
              return baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
            };
          }
          return iteratee;
        });
      } else {
        iteratees = [identity];
      }

      var index = -1;
      iteratees = arrayMap(iteratees, baseUnary(getIteratee()));

      var result = baseMap(collection, function(value, key, collection) {
        var criteria = arrayMap(iteratees, function(iteratee) {
          return iteratee(value);
        });
        return { 'criteria': criteria, 'index': ++index, 'value': value };
      });

      return baseSortBy(result, function(object, other) {
        return compareMultiple(object, other, orders);
      });
    }

    /**
     * The base implementation of `_.pick` without support for individual
     * property identifiers.
     *
     * @private
     * @param {Object} object The source object.
     * @param {string[]} paths The property paths to pick.
     * @returns {Object} Returns the new object.
     */
    function basePick(object, paths) {
      return basePickBy(object, paths, function(value, path) {
        return hasIn(object, path);
      });
    }

    /**
     * The base implementation of  `_.pickBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Object} object The source object.
     * @param {string[]} paths The property paths to pick.
     * @param {Function} predicate The function invoked per property.
     * @returns {Object} Returns the new object.
     */
    function basePickBy(object, paths, predicate) {
      var index = -1,
          length = paths.length,
          result = {};

      while (++index < length) {
        var path = paths[index],
            value = baseGet(object, path);

        if (predicate(value, path)) {
          baseSet(result, castPath(path, object), value);
        }
      }
      return result;
    }

    /**
     * A specialized version of `baseProperty` which supports deep paths.
     *
     * @private
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     */
    function basePropertyDeep(path) {
      return function(object) {
        return baseGet(object, path);
      };
    }

    /**
     * The base implementation of `_.pullAllBy` without support for iteratee
     * shorthands.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     */
    function basePullAll(array, values, iteratee, comparator) {
      var indexOf = comparator ? baseIndexOfWith : baseIndexOf,
          index = -1,
          length = values.length,
          seen = array;

      if (array === values) {
        values = copyArray(values);
      }
      if (iteratee) {
        seen = arrayMap(array, baseUnary(iteratee));
      }
      while (++index < length) {
        var fromIndex = 0,
            value = values[index],
            computed = iteratee ? iteratee(value) : value;

        while ((fromIndex = indexOf(seen, computed, fromIndex, comparator)) > -1) {
          if (seen !== array) {
            splice.call(seen, fromIndex, 1);
          }
          splice.call(array, fromIndex, 1);
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.pullAt` without support for individual
     * indexes or capturing the removed elements.
     *
     * @private
     * @param {Array} array The array to modify.
     * @param {number[]} indexes The indexes of elements to remove.
     * @returns {Array} Returns `array`.
     */
    function basePullAt(array, indexes) {
      var length = array ? indexes.length : 0,
          lastIndex = length - 1;

      while (length--) {
        var index = indexes[length];
        if (length == lastIndex || index !== previous) {
          var previous = index;
          if (isIndex(index)) {
            splice.call(array, index, 1);
          } else {
            baseUnset(array, index);
          }
        }
      }
      return array;
    }

    /**
     * The base implementation of `_.random` without support for returning
     * floating-point numbers.
     *
     * @private
     * @param {number} lower The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the random number.
     */
    function baseRandom(lower, upper) {
      return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
    }

    /**
     * The base implementation of `_.range` and `_.rangeRight` which doesn't
     * coerce arguments.
     *
     * @private
     * @param {number} start The start of the range.
     * @param {number} end The end of the range.
     * @param {number} step The value to increment or decrement by.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the range of numbers.
     */
    function baseRange(start, end, step, fromRight) {
      var index = -1,
          length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
          result = Array(length);

      while (length--) {
        result[fromRight ? length : ++index] = start;
        start += step;
      }
      return result;
    }

    /**
     * The base implementation of `_.repeat` which doesn't coerce arguments.
     *
     * @private
     * @param {string} string The string to repeat.
     * @param {number} n The number of times to repeat the string.
     * @returns {string} Returns the repeated string.
     */
    function baseRepeat(string, n) {
      var result = '';
      if (!string || n < 1 || n > MAX_SAFE_INTEGER) {
        return result;
      }
      // Leverage the exponentiation by squaring algorithm for a faster repeat.
      // See https://en.wikipedia.org/wiki/Exponentiation_by_squaring for more details.
      do {
        if (n % 2) {
          result += string;
        }
        n = nativeFloor(n / 2);
        if (n) {
          string += string;
        }
      } while (n);

      return result;
    }

    /**
     * The base implementation of `_.rest` which doesn't validate or coerce arguments.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @returns {Function} Returns the new function.
     */
    function baseRest(func, start) {
      return setToString(overRest(func, start, identity), func + '');
    }

    /**
     * The base implementation of `_.sample`.
     *
     * @private
     * @param {Array|Object} collection The collection to sample.
     * @returns {*} Returns the random element.
     */
    function baseSample(collection) {
      return arraySample(values(collection));
    }

    /**
     * The base implementation of `_.sampleSize` without param guards.
     *
     * @private
     * @param {Array|Object} collection The collection to sample.
     * @param {number} n The number of elements to sample.
     * @returns {Array} Returns the random elements.
     */
    function baseSampleSize(collection, n) {
      var array = values(collection);
      return shuffleSelf(array, baseClamp(n, 0, array.length));
    }

    /**
     * The base implementation of `_.set`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseSet(object, path, value, customizer) {
      if (!isObject(object)) {
        return object;
      }
      path = castPath(path, object);

      var index = -1,
          length = path.length,
          lastIndex = length - 1,
          nested = object;

      while (nested != null && ++index < length) {
        var key = toKey(path[index]),
            newValue = value;

        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          return object;
        }

        if (index != lastIndex) {
          var objValue = nested[key];
          newValue = customizer ? customizer(objValue, key, nested) : undefined;
          if (newValue === undefined) {
            newValue = isObject(objValue)
              ? objValue
              : (isIndex(path[index + 1]) ? [] : {});
          }
        }
        assignValue(nested, key, newValue);
        nested = nested[key];
      }
      return object;
    }

    /**
     * The base implementation of `setData` without support for hot loop shorting.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var baseSetData = !metaMap ? identity : function(func, data) {
      metaMap.set(func, data);
      return func;
    };

    /**
     * The base implementation of `setToString` without support for hot loop shorting.
     *
     * @private
     * @param {Function} func The function to modify.
     * @param {Function} string The `toString` result.
     * @returns {Function} Returns `func`.
     */
    var baseSetToString = !defineProperty ? identity : function(func, string) {
      return defineProperty(func, 'toString', {
        'configurable': true,
        'enumerable': false,
        'value': constant(string),
        'writable': true
      });
    };

    /**
     * The base implementation of `_.shuffle`.
     *
     * @private
     * @param {Array|Object} collection The collection to shuffle.
     * @returns {Array} Returns the new shuffled array.
     */
    function baseShuffle(collection) {
      return shuffleSelf(values(collection));
    }

    /**
     * The base implementation of `_.slice` without an iteratee call guard.
     *
     * @private
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseSlice(array, start, end) {
      var index = -1,
          length = array.length;

      if (start < 0) {
        start = -start > length ? 0 : (length + start);
      }
      end = end > length ? length : end;
      if (end < 0) {
        end += length;
      }
      length = start > end ? 0 : ((end - start) >>> 0);
      start >>>= 0;

      var result = Array(length);
      while (++index < length) {
        result[index] = array[index + start];
      }
      return result;
    }

    /**
     * The base implementation of `_.some` without support for iteratee shorthands.
     *
     * @private
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} predicate The function invoked per iteration.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     */
    function baseSome(collection, predicate) {
      var result;

      baseEach(collection, function(value, index, collection) {
        result = predicate(value, index, collection);
        return !result;
      });
      return !!result;
    }

    /**
     * The base implementation of `_.sortedIndex` and `_.sortedLastIndex` which
     * performs a binary search of `array` to determine the index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndex(array, value, retHighest) {
      var low = 0,
          high = array == null ? low : array.length;

      if (typeof value == 'number' && value === value && high <= HALF_MAX_ARRAY_LENGTH) {
        while (low < high) {
          var mid = (low + high) >>> 1,
              computed = array[mid];

          if (computed !== null && !isSymbol(computed) &&
              (retHighest ? (computed <= value) : (computed < value))) {
            low = mid + 1;
          } else {
            high = mid;
          }
        }
        return high;
      }
      return baseSortedIndexBy(array, value, identity, retHighest);
    }

    /**
     * The base implementation of `_.sortedIndexBy` and `_.sortedLastIndexBy`
     * which invokes `iteratee` for `value` and each element of `array` to compute
     * their sort ranking. The iteratee is invoked with one argument; (value).
     *
     * @private
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} iteratee The iteratee invoked per element.
     * @param {boolean} [retHighest] Specify returning the highest qualified index.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     */
    function baseSortedIndexBy(array, value, iteratee, retHighest) {
      var low = 0,
          high = array == null ? 0 : array.length;
      if (high === 0) {
        return 0;
      }

      value = iteratee(value);
      var valIsNaN = value !== value,
          valIsNull = value === null,
          valIsSymbol = isSymbol(value),
          valIsUndefined = value === undefined;

      while (low < high) {
        var mid = nativeFloor((low + high) / 2),
            computed = iteratee(array[mid]),
            othIsDefined = computed !== undefined,
            othIsNull = computed === null,
            othIsReflexive = computed === computed,
            othIsSymbol = isSymbol(computed);

        if (valIsNaN) {
          var setLow = retHighest || othIsReflexive;
        } else if (valIsUndefined) {
          setLow = othIsReflexive && (retHighest || othIsDefined);
        } else if (valIsNull) {
          setLow = othIsReflexive && othIsDefined && (retHighest || !othIsNull);
        } else if (valIsSymbol) {
          setLow = othIsReflexive && othIsDefined && !othIsNull && (retHighest || !othIsSymbol);
        } else if (othIsNull || othIsSymbol) {
          setLow = false;
        } else {
          setLow = retHighest ? (computed <= value) : (computed < value);
        }
        if (setLow) {
          low = mid + 1;
        } else {
          high = mid;
        }
      }
      return nativeMin(high, MAX_ARRAY_INDEX);
    }

    /**
     * The base implementation of `_.sortedUniq` and `_.sortedUniqBy` without
     * support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseSortedUniq(array, iteratee) {
      var index = -1,
          length = array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        if (!index || !eq(computed, seen)) {
          var seen = computed;
          result[resIndex++] = value === 0 ? 0 : value;
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.toNumber` which doesn't ensure correct
     * conversions of binary, hexadecimal, or octal string values.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     */
    function baseToNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      return +value;
    }

    /**
     * The base implementation of `_.toString` which doesn't convert nullish
     * values to empty strings.
     *
     * @private
     * @param {*} value The value to process.
     * @returns {string} Returns the string.
     */
    function baseToString(value) {
      // Exit early for strings to avoid a performance hit in some environments.
      if (typeof value == 'string') {
        return value;
      }
      if (isArray(value)) {
        // Recursively convert values (susceptible to call stack limits).
        return arrayMap(value, baseToString) + '';
      }
      if (isSymbol(value)) {
        return symbolToString ? symbolToString.call(value) : '';
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * The base implementation of `_.uniqBy` without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     */
    function baseUniq(array, iteratee, comparator) {
      var index = -1,
          includes = arrayIncludes,
          length = array.length,
          isCommon = true,
          result = [],
          seen = result;

      if (comparator) {
        isCommon = false;
        includes = arrayIncludesWith;
      }
      else if (length >= LARGE_ARRAY_SIZE) {
        var set = iteratee ? null : createSet(array);
        if (set) {
          return setToArray(set);
        }
        isCommon = false;
        includes = cacheHas;
        seen = new SetCache;
      }
      else {
        seen = iteratee ? [] : result;
      }
      outer:
      while (++index < length) {
        var value = array[index],
            computed = iteratee ? iteratee(value) : value;

        value = (comparator || value !== 0) ? value : 0;
        if (isCommon && computed === computed) {
          var seenIndex = seen.length;
          while (seenIndex--) {
            if (seen[seenIndex] === computed) {
              continue outer;
            }
          }
          if (iteratee) {
            seen.push(computed);
          }
          result.push(value);
        }
        else if (!includes(seen, computed, comparator)) {
          if (seen !== result) {
            seen.push(computed);
          }
          result.push(value);
        }
      }
      return result;
    }

    /**
     * The base implementation of `_.unset`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The property path to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     */
    function baseUnset(object, path) {
      path = castPath(path, object);

      // Prevent prototype pollution, see: https://github.com/lodash/lodash/security/advisories/GHSA-xxjr-mmjv-4gpg
      var index = -1,
          length = path.length;

      if (!length) {
        return true;
      }

      var isRootPrimitive = object == null || (typeof object !== 'object' && typeof object !== 'function');

      while (++index < length) {
        var key = path[index];

        // skip non-string keys (e.g., Symbols, numbers)
        if (typeof key !== 'string') {
          continue;
        }

        // Always block "__proto__" anywhere in the path if it's not expected
        if (key === '__proto__' && !hasOwnProperty.call(object, '__proto__')) {
          return false;
        }

        // Block "constructor.prototype" chains
        if (key === 'constructor' &&
            (index + 1) < length &&
            typeof path[index + 1] === 'string' &&
            path[index + 1] === 'prototype') {

          // Allow ONLY when the path starts at a primitive root, e.g., _.unset(0, 'constructor.prototype.a')
          if (isRootPrimitive && index === 0) {
            continue;
          }

          return false;
        }
      }

      var obj = parent(object, path);
      return obj == null || delete obj[toKey(last(path))];
    }

    /**
     * The base implementation of `_.update`.
     *
     * @private
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to update.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize path creation.
     * @returns {Object} Returns `object`.
     */
    function baseUpdate(object, path, updater, customizer) {
      return baseSet(object, path, updater(baseGet(object, path)), customizer);
    }

    /**
     * The base implementation of methods like `_.dropWhile` and `_.takeWhile`
     * without support for iteratee shorthands.
     *
     * @private
     * @param {Array} array The array to query.
     * @param {Function} predicate The function invoked per iteration.
     * @param {boolean} [isDrop] Specify dropping elements instead of taking them.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Array} Returns the slice of `array`.
     */
    function baseWhile(array, predicate, isDrop, fromRight) {
      var length = array.length,
          index = fromRight ? length : -1;

      while ((fromRight ? index-- : ++index < length) &&
        predicate(array[index], index, array)) {}

      return isDrop
        ? baseSlice(array, (fromRight ? 0 : index), (fromRight ? index + 1 : length))
        : baseSlice(array, (fromRight ? index + 1 : 0), (fromRight ? length : index));
    }

    /**
     * The base implementation of `wrapperValue` which returns the result of
     * performing a sequence of actions on the unwrapped `value`, where each
     * successive action is supplied the return value of the previous.
     *
     * @private
     * @param {*} value The unwrapped value.
     * @param {Array} actions Actions to perform to resolve the unwrapped value.
     * @returns {*} Returns the resolved value.
     */
    function baseWrapperValue(value, actions) {
      var result = value;
      if (result instanceof LazyWrapper) {
        result = result.value();
      }
      return arrayReduce(actions, function(result, action) {
        return action.func.apply(action.thisArg, arrayPush([result], action.args));
      }, result);
    }

    /**
     * The base implementation of methods like `_.xor`, without support for
     * iteratee shorthands, that accepts an array of arrays to inspect.
     *
     * @private
     * @param {Array} arrays The arrays to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of values.
     */
    function baseXor(arrays, iteratee, comparator) {
      var length = arrays.length;
      if (length < 2) {
        return length ? baseUniq(arrays[0]) : [];
      }
      var index = -1,
          result = Array(length);

      while (++index < length) {
        var array = arrays[index],
            othIndex = -1;

        while (++othIndex < length) {
          if (othIndex != index) {
            result[index] = baseDifference(result[index] || array, arrays[othIndex], iteratee, comparator);
          }
        }
      }
      return baseUniq(baseFlatten(result, 1), iteratee, comparator);
    }

    /**
     * This base implementation of `_.zipObject` which assigns values using `assignFunc`.
     *
     * @private
     * @param {Array} props The property identifiers.
     * @param {Array} values The property values.
     * @param {Function} assignFunc The function to assign values.
     * @returns {Object} Returns the new object.
     */
    function baseZipObject(props, values, assignFunc) {
      var index = -1,
          length = props.length,
          valsLength = values.length,
          result = {};

      while (++index < length) {
        var value = index < valsLength ? values[index] : undefined;
        assignFunc(result, props[index], value);
      }
      return result;
    }

    /**
     * Casts `value` to an empty array if it's not an array like object.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Array|Object} Returns the cast array-like object.
     */
    function castArrayLikeObject(value) {
      return isArrayLikeObject(value) ? value : [];
    }

    /**
     * Casts `value` to `identity` if it's not a function.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {Function} Returns cast function.
     */
    function castFunction(value) {
      return typeof value == 'function' ? value : identity;
    }

    /**
     * Casts `value` to a path array if it's not one.
     *
     * @private
     * @param {*} value The value to inspect.
     * @param {Object} [object] The object to query keys on.
     * @returns {Array} Returns the cast property path array.
     */
    function castPath(value, object) {
      if (isArray(value)) {
        return value;
      }
      return isKey(value, object) ? [value] : stringToPath(toString(value));
    }

    /**
     * A `baseRest` alias which can be replaced with `identity` by module
     * replacement plugins.
     *
     * @private
     * @type {Function}
     * @param {Function} func The function to apply a rest parameter to.
     * @returns {Function} Returns the new function.
     */
    var castRest = baseRest;

    /**
     * Casts `array` to a slice if it's needed.
     *
     * @private
     * @param {Array} array The array to inspect.
     * @param {number} start The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the cast slice.
     */
    function castSlice(array, start, end) {
      var length = array.length;
      end = end === undefined ? length : end;
      return (!start && end >= length) ? array : baseSlice(array, start, end);
    }

    /**
     * A simple wrapper around the global [`clearTimeout`](https://mdn.io/clearTimeout).
     *
     * @private
     * @param {number|Object} id The timer id or timeout object of the timer to clear.
     */
    var clearTimeout = ctxClearTimeout || function(id) {
      return root.clearTimeout(id);
    };

    /**
     * Creates a clone of  `buffer`.
     *
     * @private
     * @param {Buffer} buffer The buffer to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Buffer} Returns the cloned buffer.
     */
    function cloneBuffer(buffer, isDeep) {
      if (isDeep) {
        return buffer.slice();
      }
      var length = buffer.length,
          result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

      buffer.copy(result);
      return result;
    }

    /**
     * Creates a clone of `arrayBuffer`.
     *
     * @private
     * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
     * @returns {ArrayBuffer} Returns the cloned array buffer.
     */
    function cloneArrayBuffer(arrayBuffer) {
      var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
      new Uint8Array(result).set(new Uint8Array(arrayBuffer));
      return result;
    }

    /**
     * Creates a clone of `dataView`.
     *
     * @private
     * @param {Object} dataView The data view to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned data view.
     */
    function cloneDataView(dataView, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(dataView.buffer) : dataView.buffer;
      return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
    }

    /**
     * Creates a clone of `regexp`.
     *
     * @private
     * @param {Object} regexp The regexp to clone.
     * @returns {Object} Returns the cloned regexp.
     */
    function cloneRegExp(regexp) {
      var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
      result.lastIndex = regexp.lastIndex;
      return result;
    }

    /**
     * Creates a clone of the `symbol` object.
     *
     * @private
     * @param {Object} symbol The symbol object to clone.
     * @returns {Object} Returns the cloned symbol object.
     */
    function cloneSymbol(symbol) {
      return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
    }

    /**
     * Creates a clone of `typedArray`.
     *
     * @private
     * @param {Object} typedArray The typed array to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the cloned typed array.
     */
    function cloneTypedArray(typedArray, isDeep) {
      var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
      return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
    }

    /**
     * Compares values to sort them in ascending order.
     *
     * @private
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {number} Returns the sort order indicator for `value`.
     */
    function compareAscending(value, other) {
      if (value !== other) {
        var valIsDefined = value !== undefined,
            valIsNull = value === null,
            valIsReflexive = value === value,
            valIsSymbol = isSymbol(value);

        var othIsDefined = other !== undefined,
            othIsNull = other === null,
            othIsReflexive = other === other,
            othIsSymbol = isSymbol(other);

        if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
            (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
            (valIsNull && othIsDefined && othIsReflexive) ||
            (!valIsDefined && othIsReflexive) ||
            !valIsReflexive) {
          return 1;
        }
        if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
            (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
            (othIsNull && valIsDefined && valIsReflexive) ||
            (!othIsDefined && valIsReflexive) ||
            !othIsReflexive) {
          return -1;
        }
      }
      return 0;
    }

    /**
     * Used by `_.orderBy` to compare multiple properties of a value to another
     * and stable sort them.
     *
     * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
     * specify an order of "desc" for descending or "asc" for ascending sort order
     * of corresponding values.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {boolean[]|string[]} orders The order to sort by for each property.
     * @returns {number} Returns the sort order indicator for `object`.
     */
    function compareMultiple(object, other, orders) {
      var index = -1,
          objCriteria = object.criteria,
          othCriteria = other.criteria,
          length = objCriteria.length,
          ordersLength = orders.length;

      while (++index < length) {
        var result = compareAscending(objCriteria[index], othCriteria[index]);
        if (result) {
          if (index >= ordersLength) {
            return result;
          }
          var order = orders[index];
          return result * (order == 'desc' ? -1 : 1);
        }
      }
      // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
      // that causes it, under certain circumstances, to provide the same value for
      // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
      // for more details.
      //
      // This also ensures a stable sort in V8 and other engines.
      // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
      return object.index - other.index;
    }

    /**
     * Creates an array that is the composition of partially applied arguments,
     * placeholders, and provided arguments into a single array of arguments.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to prepend to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgs(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersLength = holders.length,
          leftIndex = -1,
          leftLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(leftLength + rangeLength),
          isUncurried = !isCurried;

      while (++leftIndex < leftLength) {
        result[leftIndex] = partials[leftIndex];
      }
      while (++argsIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[holders[argsIndex]] = args[argsIndex];
        }
      }
      while (rangeLength--) {
        result[leftIndex++] = args[argsIndex++];
      }
      return result;
    }

    /**
     * This function is like `composeArgs` except that the arguments composition
     * is tailored for `_.partialRight`.
     *
     * @private
     * @param {Array} args The provided arguments.
     * @param {Array} partials The arguments to append to those provided.
     * @param {Array} holders The `partials` placeholder indexes.
     * @params {boolean} [isCurried] Specify composing for a curried function.
     * @returns {Array} Returns the new array of composed arguments.
     */
    function composeArgsRight(args, partials, holders, isCurried) {
      var argsIndex = -1,
          argsLength = args.length,
          holdersIndex = -1,
          holdersLength = holders.length,
          rightIndex = -1,
          rightLength = partials.length,
          rangeLength = nativeMax(argsLength - holdersLength, 0),
          result = Array(rangeLength + rightLength),
          isUncurried = !isCurried;

      while (++argsIndex < rangeLength) {
        result[argsIndex] = args[argsIndex];
      }
      var offset = argsIndex;
      while (++rightIndex < rightLength) {
        result[offset + rightIndex] = partials[rightIndex];
      }
      while (++holdersIndex < holdersLength) {
        if (isUncurried || argsIndex < argsLength) {
          result[offset + holders[holdersIndex]] = args[argsIndex++];
        }
      }
      return result;
    }

    /**
     * Copies the values of `source` to `array`.
     *
     * @private
     * @param {Array} source The array to copy values from.
     * @param {Array} [array=[]] The array to copy values to.
     * @returns {Array} Returns `array`.
     */
    function copyArray(source, array) {
      var index = -1,
          length = source.length;

      array || (array = Array(length));
      while (++index < length) {
        array[index] = source[index];
      }
      return array;
    }

    /**
     * Copies properties of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy properties from.
     * @param {Array} props The property identifiers to copy.
     * @param {Object} [object={}] The object to copy properties to.
     * @param {Function} [customizer] The function to customize copied values.
     * @returns {Object} Returns `object`.
     */
    function copyObject(source, props, object, customizer) {
      var isNew = !object;
      object || (object = {});

      var index = -1,
          length = props.length;

      while (++index < length) {
        var key = props[index];

        var newValue = customizer
          ? customizer(object[key], source[key], key, object, source)
          : undefined;

        if (newValue === undefined) {
          newValue = source[key];
        }
        if (isNew) {
          baseAssignValue(object, key, newValue);
        } else {
          assignValue(object, key, newValue);
        }
      }
      return object;
    }

    /**
     * Copies own symbols of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy symbols from.
     * @param {Object} [object={}] The object to copy symbols to.
     * @returns {Object} Returns `object`.
     */
    function copySymbols(source, object) {
      return copyObject(source, getSymbols(source), object);
    }

    /**
     * Copies own and inherited symbols of `source` to `object`.
     *
     * @private
     * @param {Object} source The object to copy symbols from.
     * @param {Object} [object={}] The object to copy symbols to.
     * @returns {Object} Returns `object`.
     */
    function copySymbolsIn(source, object) {
      return copyObject(source, getSymbolsIn(source), object);
    }

    /**
     * Creates a function like `_.groupBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} [initializer] The accumulator object initializer.
     * @returns {Function} Returns the new aggregator function.
     */
    function createAggregator(setter, initializer) {
      return function(collection, iteratee) {
        var func = isArray(collection) ? arrayAggregator : baseAggregator,
            accumulator = initializer ? initializer() : {};

        return func(collection, setter, getIteratee(iteratee, 2), accumulator);
      };
    }

    /**
     * Creates a function like `_.assign`.
     *
     * @private
     * @param {Function} assigner The function to assign values.
     * @returns {Function} Returns the new assigner function.
     */
    function createAssigner(assigner) {
      return baseRest(function(object, sources) {
        var index = -1,
            length = sources.length,
            customizer = length > 1 ? sources[length - 1] : undefined,
            guard = length > 2 ? sources[2] : undefined;

        customizer = (assigner.length > 3 && typeof customizer == 'function')
          ? (length--, customizer)
          : undefined;

        if (guard && isIterateeCall(sources[0], sources[1], guard)) {
          customizer = length < 3 ? undefined : customizer;
          length = 1;
        }
        object = Object(object);
        while (++index < length) {
          var source = sources[index];
          if (source) {
            assigner(object, source, index, customizer);
          }
        }
        return object;
      });
    }

    /**
     * Creates a `baseEach` or `baseEachRight` function.
     *
     * @private
     * @param {Function} eachFunc The function to iterate over a collection.
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseEach(eachFunc, fromRight) {
      return function(collection, iteratee) {
        if (collection == null) {
          return collection;
        }
        if (!isArrayLike(collection)) {
          return eachFunc(collection, iteratee);
        }
        var length = collection.length,
            index = fromRight ? length : -1,
            iterable = Object(collection);

        while ((fromRight ? index-- : ++index < length)) {
          if (iteratee(iterable[index], index, iterable) === false) {
            break;
          }
        }
        return collection;
      };
    }

    /**
     * Creates a base function for methods like `_.forIn` and `_.forOwn`.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new base function.
     */
    function createBaseFor(fromRight) {
      return function(object, iteratee, keysFunc) {
        var index = -1,
            iterable = Object(object),
            props = keysFunc(object),
            length = props.length;

        while (length--) {
          var key = props[fromRight ? length : ++index];
          if (iteratee(iterable[key], key, iterable) === false) {
            break;
          }
        }
        return object;
      };
    }

    /**
     * Creates a function that wraps `func` to invoke it with the optional `this`
     * binding of `thisArg`.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createBind(func, bitmask, thisArg) {
      var isBind = bitmask & WRAP_BIND_FLAG,
          Ctor = createCtor(func);

      function wrapper() {
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return fn.apply(isBind ? thisArg : this, arguments);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.lowerFirst`.
     *
     * @private
     * @param {string} methodName The name of the `String` case method to use.
     * @returns {Function} Returns the new case function.
     */
    function createCaseFirst(methodName) {
      return function(string) {
        string = toString(string);

        var strSymbols = hasUnicode(string)
          ? stringToArray(string)
          : undefined;

        var chr = strSymbols
          ? strSymbols[0]
          : string.charAt(0);

        var trailing = strSymbols
          ? castSlice(strSymbols, 1).join('')
          : string.slice(1);

        return chr[methodName]() + trailing;
      };
    }

    /**
     * Creates a function like `_.camelCase`.
     *
     * @private
     * @param {Function} callback The function to combine each word.
     * @returns {Function} Returns the new compounder function.
     */
    function createCompounder(callback) {
      return function(string) {
        return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
      };
    }

    /**
     * Creates a function that produces an instance of `Ctor` regardless of
     * whether it was invoked as part of a `new` expression or by `call` or `apply`.
     *
     * @private
     * @param {Function} Ctor The constructor to wrap.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCtor(Ctor) {
      return function() {
        // Use a `switch` statement to work with class constructors. See
        // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
        // for more details.
        var args = arguments;
        switch (args.length) {
          case 0: return new Ctor;
          case 1: return new Ctor(args[0]);
          case 2: return new Ctor(args[0], args[1]);
          case 3: return new Ctor(args[0], args[1], args[2]);
          case 4: return new Ctor(args[0], args[1], args[2], args[3]);
          case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
          case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
          case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
        }
        var thisBinding = baseCreate(Ctor.prototype),
            result = Ctor.apply(thisBinding, args);

        // Mimic the constructor's `return` behavior.
        // See https://es5.github.io/#x13.2.2 for more details.
        return isObject(result) ? result : thisBinding;
      };
    }

    /**
     * Creates a function that wraps `func` to enable currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {number} arity The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createCurry(func, bitmask, arity) {
      var Ctor = createCtor(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length,
            placeholder = getHolder(wrapper);

        while (index--) {
          args[index] = arguments[index];
        }
        var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
          ? []
          : replaceHolders(args, placeholder);

        length -= holders.length;
        if (length < arity) {
          return createRecurry(
            func, bitmask, createHybrid, wrapper.placeholder, undefined,
            args, holders, undefined, undefined, arity - length);
        }
        var fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;
        return apply(fn, this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.find` or `_.findLast` function.
     *
     * @private
     * @param {Function} findIndexFunc The function to find the collection index.
     * @returns {Function} Returns the new find function.
     */
    function createFind(findIndexFunc) {
      return function(collection, predicate, fromIndex) {
        var iterable = Object(collection);
        if (!isArrayLike(collection)) {
          var iteratee = getIteratee(predicate, 3);
          collection = keys(collection);
          predicate = function(key) { return iteratee(iterable[key], key, iterable); };
        }
        var index = findIndexFunc(collection, predicate, fromIndex);
        return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
      };
    }

    /**
     * Creates a `_.flow` or `_.flowRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new flow function.
     */
    function createFlow(fromRight) {
      return flatRest(function(funcs) {
        var length = funcs.length,
            index = length,
            prereq = LodashWrapper.prototype.thru;

        if (fromRight) {
          funcs.reverse();
        }
        while (index--) {
          var func = funcs[index];
          if (typeof func != 'function') {
            throw new TypeError(FUNC_ERROR_TEXT);
          }
          if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
            var wrapper = new LodashWrapper([], true);
          }
        }
        index = wrapper ? index : length;
        while (++index < length) {
          func = funcs[index];

          var funcName = getFuncName(func),
              data = funcName == 'wrapper' ? getData(func) : undefined;

          if (data && isLaziable(data[0]) &&
                data[1] == (WRAP_ARY_FLAG | WRAP_CURRY_FLAG | WRAP_PARTIAL_FLAG | WRAP_REARG_FLAG) &&
                !data[4].length && data[9] == 1
              ) {
            wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
          } else {
            wrapper = (func.length == 1 && isLaziable(func))
              ? wrapper[funcName]()
              : wrapper.thru(func);
          }
        }
        return function() {
          var args = arguments,
              value = args[0];

          if (wrapper && args.length == 1 && isArray(value)) {
            return wrapper.plant(value).value();
          }
          var index = 0,
              result = length ? funcs[index].apply(this, args) : value;

          while (++index < length) {
            result = funcs[index].call(this, result);
          }
          return result;
        };
      });
    }

    /**
     * Creates a function that wraps `func` to invoke it with optional `this`
     * binding of `thisArg`, partial application, and currying.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [partialsRight] The arguments to append to those provided
     *  to the new function.
     * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createHybrid(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
      var isAry = bitmask & WRAP_ARY_FLAG,
          isBind = bitmask & WRAP_BIND_FLAG,
          isBindKey = bitmask & WRAP_BIND_KEY_FLAG,
          isCurried = bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG),
          isFlip = bitmask & WRAP_FLIP_FLAG,
          Ctor = isBindKey ? undefined : createCtor(func);

      function wrapper() {
        var length = arguments.length,
            args = Array(length),
            index = length;

        while (index--) {
          args[index] = arguments[index];
        }
        if (isCurried) {
          var placeholder = getHolder(wrapper),
              holdersCount = countHolders(args, placeholder);
        }
        if (partials) {
          args = composeArgs(args, partials, holders, isCurried);
        }
        if (partialsRight) {
          args = composeArgsRight(args, partialsRight, holdersRight, isCurried);
        }
        length -= holdersCount;
        if (isCurried && length < arity) {
          var newHolders = replaceHolders(args, placeholder);
          return createRecurry(
            func, bitmask, createHybrid, wrapper.placeholder, thisArg,
            args, newHolders, argPos, ary, arity - length
          );
        }
        var thisBinding = isBind ? thisArg : this,
            fn = isBindKey ? thisBinding[func] : func;

        length = args.length;
        if (argPos) {
          args = reorder(args, argPos);
        } else if (isFlip && length > 1) {
          args.reverse();
        }
        if (isAry && ary < length) {
          args.length = ary;
        }
        if (this && this !== root && this instanceof wrapper) {
          fn = Ctor || createCtor(fn);
        }
        return fn.apply(thisBinding, args);
      }
      return wrapper;
    }

    /**
     * Creates a function like `_.invertBy`.
     *
     * @private
     * @param {Function} setter The function to set accumulator values.
     * @param {Function} toIteratee The function to resolve iteratees.
     * @returns {Function} Returns the new inverter function.
     */
    function createInverter(setter, toIteratee) {
      return function(object, iteratee) {
        return baseInverter(object, setter, toIteratee(iteratee), {});
      };
    }

    /**
     * Creates a function that performs a mathematical operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @param {number} [defaultValue] The value used for `undefined` arguments.
     * @returns {Function} Returns the new mathematical operation function.
     */
    function createMathOperation(operator, defaultValue) {
      return function(value, other) {
        var result;
        if (value === undefined && other === undefined) {
          return defaultValue;
        }
        if (value !== undefined) {
          result = value;
        }
        if (other !== undefined) {
          if (result === undefined) {
            return other;
          }
          if (typeof value == 'string' || typeof other == 'string') {
            value = baseToString(value);
            other = baseToString(other);
          } else {
            value = baseToNumber(value);
            other = baseToNumber(other);
          }
          result = operator(value, other);
        }
        return result;
      };
    }

    /**
     * Creates a function like `_.over`.
     *
     * @private
     * @param {Function} arrayFunc The function to iterate over iteratees.
     * @returns {Function} Returns the new over function.
     */
    function createOver(arrayFunc) {
      return flatRest(function(iteratees) {
        iteratees = arrayMap(iteratees, baseUnary(getIteratee()));
        return baseRest(function(args) {
          var thisArg = this;
          return arrayFunc(iteratees, function(iteratee) {
            return apply(iteratee, thisArg, args);
          });
        });
      });
    }

    /**
     * Creates the padding for `string` based on `length`. The `chars` string
     * is truncated if the number of characters exceeds `length`.
     *
     * @private
     * @param {number} length The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padding for `string`.
     */
    function createPadding(length, chars) {
      chars = chars === undefined ? ' ' : baseToString(chars);

      var charsLength = chars.length;
      if (charsLength < 2) {
        return charsLength ? baseRepeat(chars, length) : chars;
      }
      var result = baseRepeat(chars, nativeCeil(length / stringSize(chars)));
      return hasUnicode(chars)
        ? castSlice(stringToArray(result), 0, length).join('')
        : result.slice(0, length);
    }

    /**
     * Creates a function that wraps `func` to invoke it with the `this` binding
     * of `thisArg` and `partials` prepended to the arguments it receives.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {Array} partials The arguments to prepend to those provided to
     *  the new function.
     * @returns {Function} Returns the new wrapped function.
     */
    function createPartial(func, bitmask, thisArg, partials) {
      var isBind = bitmask & WRAP_BIND_FLAG,
          Ctor = createCtor(func);

      function wrapper() {
        var argsIndex = -1,
            argsLength = arguments.length,
            leftIndex = -1,
            leftLength = partials.length,
            args = Array(leftLength + argsLength),
            fn = (this && this !== root && this instanceof wrapper) ? Ctor : func;

        while (++leftIndex < leftLength) {
          args[leftIndex] = partials[leftIndex];
        }
        while (argsLength--) {
          args[leftIndex++] = arguments[++argsIndex];
        }
        return apply(fn, isBind ? thisArg : this, args);
      }
      return wrapper;
    }

    /**
     * Creates a `_.range` or `_.rangeRight` function.
     *
     * @private
     * @param {boolean} [fromRight] Specify iterating from right to left.
     * @returns {Function} Returns the new range function.
     */
    function createRange(fromRight) {
      return function(start, end, step) {
        if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
          end = step = undefined;
        }
        // Ensure the sign of `-0` is preserved.
        start = toFinite(start);
        if (end === undefined) {
          end = start;
          start = 0;
        } else {
          end = toFinite(end);
        }
        step = step === undefined ? (start < end ? 1 : -1) : toFinite(step);
        return baseRange(start, end, step, fromRight);
      };
    }

    /**
     * Creates a function that performs a relational operation on two values.
     *
     * @private
     * @param {Function} operator The function to perform the operation.
     * @returns {Function} Returns the new relational operation function.
     */
    function createRelationalOperation(operator) {
      return function(value, other) {
        if (!(typeof value == 'string' && typeof other == 'string')) {
          value = toNumber(value);
          other = toNumber(other);
        }
        return operator(value, other);
      };
    }

    /**
     * Creates a function that wraps `func` to continue currying.
     *
     * @private
     * @param {Function} func The function to wrap.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @param {Function} wrapFunc The function to create the `func` wrapper.
     * @param {*} placeholder The placeholder value.
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to prepend to those provided to
     *  the new function.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createRecurry(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
      var isCurry = bitmask & WRAP_CURRY_FLAG,
          newHolders = isCurry ? holders : undefined,
          newHoldersRight = isCurry ? undefined : holders,
          newPartials = isCurry ? partials : undefined,
          newPartialsRight = isCurry ? undefined : partials;

      bitmask |= (isCurry ? WRAP_PARTIAL_FLAG : WRAP_PARTIAL_RIGHT_FLAG);
      bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG : WRAP_PARTIAL_FLAG);

      if (!(bitmask & WRAP_CURRY_BOUND_FLAG)) {
        bitmask &= ~(WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG);
      }
      var newData = [
        func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
        newHoldersRight, argPos, ary, arity
      ];

      var result = wrapFunc.apply(undefined, newData);
      if (isLaziable(func)) {
        setData(result, newData);
      }
      result.placeholder = placeholder;
      return setWrapToString(result, func, bitmask);
    }

    /**
     * Creates a function like `_.round`.
     *
     * @private
     * @param {string} methodName The name of the `Math` method to use when rounding.
     * @returns {Function} Returns the new round function.
     */
    function createRound(methodName) {
      var func = Math[methodName];
      return function(number, precision) {
        number = toNumber(number);
        precision = precision == null ? 0 : nativeMin(toInteger(precision), 292);
        if (precision && nativeIsFinite(number)) {
          // Shift with exponential notation to avoid floating-point issues.
          // See [MDN](https://mdn.io/round#Examples) for more details.
          var pair = (toString(number) + 'e').split('e'),
              value = func(pair[0] + 'e' + (+pair[1] + precision));

          pair = (toString(value) + 'e').split('e');
          return +(pair[0] + 'e' + (+pair[1] - precision));
        }
        return func(number);
      };
    }

    /**
     * Creates a set object of `values`.
     *
     * @private
     * @param {Array} values The values to add to the set.
     * @returns {Object} Returns the new set.
     */
    var createSet = !(Set && (1 / setToArray(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
      return new Set(values);
    };

    /**
     * Creates a `_.toPairs` or `_.toPairsIn` function.
     *
     * @private
     * @param {Function} keysFunc The function to get the keys of a given object.
     * @returns {Function} Returns the new pairs function.
     */
    function createToPairs(keysFunc) {
      return function(object) {
        var tag = getTag(object);
        if (tag == mapTag) {
          return mapToArray(object);
        }
        if (tag == setTag) {
          return setToPairs(object);
        }
        return baseToPairs(object, keysFunc(object));
      };
    }

    /**
     * Creates a function that either curries or invokes `func` with optional
     * `this` binding and partially applied arguments.
     *
     * @private
     * @param {Function|string} func The function or method name to wrap.
     * @param {number} bitmask The bitmask flags.
     *    1 - `_.bind`
     *    2 - `_.bindKey`
     *    4 - `_.curry` or `_.curryRight` of a bound function
     *    8 - `_.curry`
     *   16 - `_.curryRight`
     *   32 - `_.partial`
     *   64 - `_.partialRight`
     *  128 - `_.rearg`
     *  256 - `_.ary`
     *  512 - `_.flip`
     * @param {*} [thisArg] The `this` binding of `func`.
     * @param {Array} [partials] The arguments to be partially applied.
     * @param {Array} [holders] The `partials` placeholder indexes.
     * @param {Array} [argPos] The argument positions of the new function.
     * @param {number} [ary] The arity cap of `func`.
     * @param {number} [arity] The arity of `func`.
     * @returns {Function} Returns the new wrapped function.
     */
    function createWrap(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
      var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
      if (!isBindKey && typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var length = partials ? partials.length : 0;
      if (!length) {
        bitmask &= ~(WRAP_PARTIAL_FLAG | WRAP_PARTIAL_RIGHT_FLAG);
        partials = holders = undefined;
      }
      ary = ary === undefined ? ary : nativeMax(toInteger(ary), 0);
      arity = arity === undefined ? arity : toInteger(arity);
      length -= holders ? holders.length : 0;

      if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
        var partialsRight = partials,
            holdersRight = holders;

        partials = holders = undefined;
      }
      var data = isBindKey ? undefined : getData(func);

      var newData = [
        func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
        argPos, ary, arity
      ];

      if (data) {
        mergeData(newData, data);
      }
      func = newData[0];
      bitmask = newData[1];
      thisArg = newData[2];
      partials = newData[3];
      holders = newData[4];
      arity = newData[9] = newData[9] === undefined
        ? (isBindKey ? 0 : func.length)
        : nativeMax(newData[9] - length, 0);

      if (!arity && bitmask & (WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG)) {
        bitmask &= ~(WRAP_CURRY_FLAG | WRAP_CURRY_RIGHT_FLAG);
      }
      if (!bitmask || bitmask == WRAP_BIND_FLAG) {
        var result = createBind(func, bitmask, thisArg);
      } else if (bitmask == WRAP_CURRY_FLAG || bitmask == WRAP_CURRY_RIGHT_FLAG) {
        result = createCurry(func, bitmask, arity);
      } else if ((bitmask == WRAP_PARTIAL_FLAG || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG)) && !holders.length) {
        result = createPartial(func, bitmask, thisArg, partials);
      } else {
        result = createHybrid.apply(undefined, newData);
      }
      var setter = data ? baseSetData : setData;
      return setWrapToString(setter(result, newData), func, bitmask);
    }

    /**
     * Used by `_.defaults` to customize its `_.assignIn` use to assign properties
     * of source objects to the destination object for all destination properties
     * that resolve to `undefined`.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to assign.
     * @param {Object} object The parent object of `objValue`.
     * @returns {*} Returns the value to assign.
     */
    function customDefaultsAssignIn(objValue, srcValue, key, object) {
      if (objValue === undefined ||
          (eq(objValue, objectProto[key]) && !hasOwnProperty.call(object, key))) {
        return srcValue;
      }
      return objValue;
    }

    /**
     * Used by `_.defaultsDeep` to customize its `_.merge` use to merge source
     * objects into destination objects that are passed thru.
     *
     * @private
     * @param {*} objValue The destination value.
     * @param {*} srcValue The source value.
     * @param {string} key The key of the property to merge.
     * @param {Object} object The parent object of `objValue`.
     * @param {Object} source The parent object of `srcValue`.
     * @param {Object} [stack] Tracks traversed source values and their merged
     *  counterparts.
     * @returns {*} Returns the value to assign.
     */
    function customDefaultsMerge(objValue, srcValue, key, object, source, stack) {
      if (isObject(objValue) && isObject(srcValue)) {
        // Recursively merge objects and arrays (susceptible to call stack limits).
        stack.set(srcValue, objValue);
        baseMerge(objValue, srcValue, undefined, customDefaultsMerge, stack);
        stack['delete'](srcValue);
      }
      return objValue;
    }

    /**
     * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
     * objects.
     *
     * @private
     * @param {*} value The value to inspect.
     * @param {string} key The key of the property to inspect.
     * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
     */
    function customOmitClone(value) {
      return isPlainObject(value) ? undefined : value;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for arrays with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Array} array The array to compare.
     * @param {Array} other The other array to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `array` and `other` objects.
     * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
     */
    function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          arrLength = array.length,
          othLength = other.length;

      if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
        return false;
      }
      // Check that cyclic values are equal.
      var arrStacked = stack.get(array);
      var othStacked = stack.get(other);
      if (arrStacked && othStacked) {
        return arrStacked == other && othStacked == array;
      }
      var index = -1,
          result = true,
          seen = (bitmask & COMPARE_UNORDERED_FLAG) ? new SetCache : undefined;

      stack.set(array, other);
      stack.set(other, array);

      // Ignore non-index properties.
      while (++index < arrLength) {
        var arrValue = array[index],
            othValue = other[index];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, arrValue, index, other, array, stack)
            : customizer(arrValue, othValue, index, array, other, stack);
        }
        if (compared !== undefined) {
          if (compared) {
            continue;
          }
          result = false;
          break;
        }
        // Recursively compare arrays (susceptible to call stack limits).
        if (seen) {
          if (!arraySome(other, function(othValue, othIndex) {
                if (!cacheHas(seen, othIndex) &&
                    (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
                  return seen.push(othIndex);
                }
              })) {
            result = false;
            break;
          }
        } else if (!(
              arrValue === othValue ||
                equalFunc(arrValue, othValue, bitmask, customizer, stack)
            )) {
          result = false;
          break;
        }
      }
      stack['delete'](array);
      stack['delete'](other);
      return result;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for comparing objects of
     * the same `toStringTag`.
     *
     * **Note:** This function only supports comparing values with tags of
     * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {string} tag The `toStringTag` of the objects to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
      switch (tag) {
        case dataViewTag:
          if ((object.byteLength != other.byteLength) ||
              (object.byteOffset != other.byteOffset)) {
            return false;
          }
          object = object.buffer;
          other = other.buffer;

        case arrayBufferTag:
          if ((object.byteLength != other.byteLength) ||
              !equalFunc(new Uint8Array(object), new Uint8Array(other))) {
            return false;
          }
          return true;

        case boolTag:
        case dateTag:
        case numberTag:
          // Coerce booleans to `1` or `0` and dates to milliseconds.
          // Invalid dates are coerced to `NaN`.
          return eq(+object, +other);

        case errorTag:
          return object.name == other.name && object.message == other.message;

        case regexpTag:
        case stringTag:
          // Coerce regexes to strings and treat strings, primitives and objects,
          // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
          // for more details.
          return object == (other + '');

        case mapTag:
          var convert = mapToArray;

        case setTag:
          var isPartial = bitmask & COMPARE_PARTIAL_FLAG;
          convert || (convert = setToArray);

          if (object.size != other.size && !isPartial) {
            return false;
          }
          // Assume cyclic values are equal.
          var stacked = stack.get(object);
          if (stacked) {
            return stacked == other;
          }
          bitmask |= COMPARE_UNORDERED_FLAG;

          // Recursively compare objects (susceptible to call stack limits).
          stack.set(object, other);
          var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
          stack['delete'](object);
          return result;

        case symbolTag:
          if (symbolValueOf) {
            return symbolValueOf.call(object) == symbolValueOf.call(other);
          }
      }
      return false;
    }

    /**
     * A specialized version of `baseIsEqualDeep` for objects with support for
     * partial deep comparisons.
     *
     * @private
     * @param {Object} object The object to compare.
     * @param {Object} other The other object to compare.
     * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
     * @param {Function} customizer The function to customize comparisons.
     * @param {Function} equalFunc The function to determine equivalents of values.
     * @param {Object} stack Tracks traversed `object` and `other` objects.
     * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
     */
    function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
          objProps = getAllKeys(object),
          objLength = objProps.length,
          othProps = getAllKeys(other),
          othLength = othProps.length;

      if (objLength != othLength && !isPartial) {
        return false;
      }
      var index = objLength;
      while (index--) {
        var key = objProps[index];
        if (!(isPartial ? key in other : hasOwnProperty.call(other, key))) {
          return false;
        }
      }
      // Check that cyclic values are equal.
      var objStacked = stack.get(object);
      var othStacked = stack.get(other);
      if (objStacked && othStacked) {
        return objStacked == other && othStacked == object;
      }
      var result = true;
      stack.set(object, other);
      stack.set(other, object);

      var skipCtor = isPartial;
      while (++index < objLength) {
        key = objProps[index];
        var objValue = object[key],
            othValue = other[key];

        if (customizer) {
          var compared = isPartial
            ? customizer(othValue, objValue, key, other, object, stack)
            : customizer(objValue, othValue, key, object, other, stack);
        }
        // Recursively compare objects (susceptible to call stack limits).
        if (!(compared === undefined
              ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
              : compared
            )) {
          result = false;
          break;
        }
        skipCtor || (skipCtor = key == 'constructor');
      }
      if (result && !skipCtor) {
        var objCtor = object.constructor,
            othCtor = other.constructor;

        // Non `Object` object instances with different constructors are not equal.
        if (objCtor != othCtor &&
            ('constructor' in object && 'constructor' in other) &&
            !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
              typeof othCtor == 'function' && othCtor instanceof othCtor)) {
          result = false;
        }
      }
      stack['delete'](object);
      stack['delete'](other);
      return result;
    }

    /**
     * A specialized version of `baseRest` which flattens the rest array.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @returns {Function} Returns the new function.
     */
    function flatRest(func) {
      return setToString(overRest(func, undefined, flatten), func + '');
    }

    /**
     * Creates an array of own enumerable property names and symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeys(object) {
      return baseGetAllKeys(object, keys, getSymbols);
    }

    /**
     * Creates an array of own and inherited enumerable property names and
     * symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names and symbols.
     */
    function getAllKeysIn(object) {
      return baseGetAllKeys(object, keysIn, getSymbolsIn);
    }

    /**
     * Gets metadata for `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {*} Returns the metadata for `func`.
     */
    var getData = !metaMap ? noop : function(func) {
      return metaMap.get(func);
    };

    /**
     * Gets the name of `func`.
     *
     * @private
     * @param {Function} func The function to query.
     * @returns {string} Returns the function name.
     */
    function getFuncName(func) {
      var result = (func.name + ''),
          array = realNames[result],
          length = hasOwnProperty.call(realNames, result) ? array.length : 0;

      while (length--) {
        var data = array[length],
            otherFunc = data.func;
        if (otherFunc == null || otherFunc == func) {
          return data.name;
        }
      }
      return result;
    }

    /**
     * Gets the argument placeholder value for `func`.
     *
     * @private
     * @param {Function} func The function to inspect.
     * @returns {*} Returns the placeholder value.
     */
    function getHolder(func) {
      var object = hasOwnProperty.call(lodash, 'placeholder') ? lodash : func;
      return object.placeholder;
    }

    /**
     * Gets the appropriate "iteratee" function. If `_.iteratee` is customized,
     * this function returns the custom method, otherwise it returns `baseIteratee`.
     * If arguments are provided, the chosen function is invoked with them and
     * its result is returned.
     *
     * @private
     * @param {*} [value] The value to convert to an iteratee.
     * @param {number} [arity] The arity of the created iteratee.
     * @returns {Function} Returns the chosen function or its result.
     */
    function getIteratee() {
      var result = lodash.iteratee || iteratee;
      result = result === iteratee ? baseIteratee : result;
      return arguments.length ? result(arguments[0], arguments[1]) : result;
    }

    /**
     * Gets the data for `map`.
     *
     * @private
     * @param {Object} map The map to query.
     * @param {string} key The reference key.
     * @returns {*} Returns the map data.
     */
    function getMapData(map, key) {
      var data = map.__data__;
      return isKeyable(key)
        ? data[typeof key == 'string' ? 'string' : 'hash']
        : data.map;
    }

    /**
     * Gets the property names, values, and compare flags of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the match data of `object`.
     */
    function getMatchData(object) {
      var result = keys(object),
          length = result.length;

      while (length--) {
        var key = result[length],
            value = object[key];

        result[length] = [key, value, isStrictComparable(value)];
      }
      return result;
    }

    /**
     * Gets the native function at `key` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the method to get.
     * @returns {*} Returns the function if it's native, else `undefined`.
     */
    function getNative(object, key) {
      var value = getValue(object, key);
      return baseIsNative(value) ? value : undefined;
    }

    /**
     * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the raw `toStringTag`.
     */
    function getRawTag(value) {
      var isOwn = hasOwnProperty.call(value, symToStringTag),
          tag = value[symToStringTag];

      try {
        value[symToStringTag] = undefined;
        var unmasked = true;
      } catch (e) {}

      var result = nativeObjectToString.call(value);
      if (unmasked) {
        if (isOwn) {
          value[symToStringTag] = tag;
        } else {
          delete value[symToStringTag];
        }
      }
      return result;
    }

    /**
     * Creates an array of the own enumerable symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
      if (object == null) {
        return [];
      }
      object = Object(object);
      return arrayFilter(nativeGetSymbols(object), function(symbol) {
        return propertyIsEnumerable.call(object, symbol);
      });
    };

    /**
     * Creates an array of the own and inherited enumerable symbols of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of symbols.
     */
    var getSymbolsIn = !nativeGetSymbols ? stubArray : function(object) {
      var result = [];
      while (object) {
        arrayPush(result, getSymbols(object));
        object = getPrototype(object);
      }
      return result;
    };

    /**
     * Gets the `toStringTag` of `value`.
     *
     * @private
     * @param {*} value The value to query.
     * @returns {string} Returns the `toStringTag`.
     */
    var getTag = baseGetTag;

    // Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
    if ((DataView && getTag(new DataView(new ArrayBuffer(1))) != dataViewTag) ||
        (Map && getTag(new Map) != mapTag) ||
        (Promise && getTag(Promise.resolve()) != promiseTag) ||
        (Set && getTag(new Set) != setTag) ||
        (WeakMap && getTag(new WeakMap) != weakMapTag)) {
      getTag = function(value) {
        var result = baseGetTag(value),
            Ctor = result == objectTag ? value.constructor : undefined,
            ctorString = Ctor ? toSource(Ctor) : '';

        if (ctorString) {
          switch (ctorString) {
            case dataViewCtorString: return dataViewTag;
            case mapCtorString: return mapTag;
            case promiseCtorString: return promiseTag;
            case setCtorString: return setTag;
            case weakMapCtorString: return weakMapTag;
          }
        }
        return result;
      };
    }

    /**
     * Gets the view, applying any `transforms` to the `start` and `end` positions.
     *
     * @private
     * @param {number} start The start of the view.
     * @param {number} end The end of the view.
     * @param {Array} transforms The transformations to apply to the view.
     * @returns {Object} Returns an object containing the `start` and `end`
     *  positions of the view.
     */
    function getView(start, end, transforms) {
      var index = -1,
          length = transforms.length;

      while (++index < length) {
        var data = transforms[index],
            size = data.size;

        switch (data.type) {
          case 'drop':      start += size; break;
          case 'dropRight': end -= size; break;
          case 'take':      end = nativeMin(end, start + size); break;
          case 'takeRight': start = nativeMax(start, end - size); break;
        }
      }
      return { 'start': start, 'end': end };
    }

    /**
     * Extracts wrapper details from the `source` body comment.
     *
     * @private
     * @param {string} source The source to inspect.
     * @returns {Array} Returns the wrapper details.
     */
    function getWrapDetails(source) {
      var match = source.match(reWrapDetails);
      return match ? match[1].split(reSplitDetails) : [];
    }

    /**
     * Checks if `path` exists on `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @param {Function} hasFunc The function to check properties.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     */
    function hasPath(object, path, hasFunc) {
      path = castPath(path, object);

      var index = -1,
          length = path.length,
          result = false;

      while (++index < length) {
        var key = toKey(path[index]);
        if (!(result = object != null && hasFunc(object, key))) {
          break;
        }
        object = object[key];
      }
      if (result || ++index != length) {
        return result;
      }
      length = object == null ? 0 : object.length;
      return !!length && isLength(length) && isIndex(key, length) &&
        (isArray(object) || isArguments(object));
    }

    /**
     * Initializes an array clone.
     *
     * @private
     * @param {Array} array The array to clone.
     * @returns {Array} Returns the initialized clone.
     */
    function initCloneArray(array) {
      var length = array.length,
          result = new array.constructor(length);

      // Add properties assigned by `RegExp#exec`.
      if (length && typeof array[0] == 'string' && hasOwnProperty.call(array, 'index')) {
        result.index = array.index;
        result.input = array.input;
      }
      return result;
    }

    /**
     * Initializes an object clone.
     *
     * @private
     * @param {Object} object The object to clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneObject(object) {
      return (typeof object.constructor == 'function' && !isPrototype(object))
        ? baseCreate(getPrototype(object))
        : {};
    }

    /**
     * Initializes an object clone based on its `toStringTag`.
     *
     * **Note:** This function only supports cloning values with tags of
     * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
     *
     * @private
     * @param {Object} object The object to clone.
     * @param {string} tag The `toStringTag` of the object to clone.
     * @param {boolean} [isDeep] Specify a deep clone.
     * @returns {Object} Returns the initialized clone.
     */
    function initCloneByTag(object, tag, isDeep) {
      var Ctor = object.constructor;
      switch (tag) {
        case arrayBufferTag:
          return cloneArrayBuffer(object);

        case boolTag:
        case dateTag:
          return new Ctor(+object);

        case dataViewTag:
          return cloneDataView(object, isDeep);

        case float32Tag: case float64Tag:
        case int8Tag: case int16Tag: case int32Tag:
        case uint8Tag: case uint8ClampedTag: case uint16Tag: case uint32Tag:
          return cloneTypedArray(object, isDeep);

        case mapTag:
          return new Ctor;

        case numberTag:
        case stringTag:
          return new Ctor(object);

        case regexpTag:
          return cloneRegExp(object);

        case setTag:
          return new Ctor;

        case symbolTag:
          return cloneSymbol(object);
      }
    }

    /**
     * Inserts wrapper `details` in a comment at the top of the `source` body.
     *
     * @private
     * @param {string} source The source to modify.
     * @returns {Array} details The details to insert.
     * @returns {string} Returns the modified source.
     */
    function insertWrapDetails(source, details) {
      var length = details.length;
      if (!length) {
        return source;
      }
      var lastIndex = length - 1;
      details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
      details = details.join(length > 2 ? ', ' : ' ');
      return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
    }

    /**
     * Checks if `value` is a flattenable `arguments` object or array.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
     */
    function isFlattenable(value) {
      return isArray(value) || isArguments(value) ||
        !!(spreadableSymbol && value && value[spreadableSymbol]);
    }

    /**
     * Checks if `value` is a valid array-like index.
     *
     * @private
     * @param {*} value The value to check.
     * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
     * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
     */
    function isIndex(value, length) {
      var type = typeof value;
      length = length == null ? MAX_SAFE_INTEGER : length;

      return !!length &&
        (type == 'number' ||
          (type != 'symbol' && reIsUint.test(value))) &&
            (value > -1 && value % 1 == 0 && value < length);
    }

    /**
     * Checks if the given arguments are from an iteratee call.
     *
     * @private
     * @param {*} value The potential iteratee value argument.
     * @param {*} index The potential iteratee index or key argument.
     * @param {*} object The potential iteratee object argument.
     * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
     *  else `false`.
     */
    function isIterateeCall(value, index, object) {
      if (!isObject(object)) {
        return false;
      }
      var type = typeof index;
      if (type == 'number'
            ? (isArrayLike(object) && isIndex(index, object.length))
            : (type == 'string' && index in object)
          ) {
        return eq(object[index], value);
      }
      return false;
    }

    /**
     * Checks if `value` is a property name and not a property path.
     *
     * @private
     * @param {*} value The value to check.
     * @param {Object} [object] The object to query keys on.
     * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
     */
    function isKey(value, object) {
      if (isArray(value)) {
        return false;
      }
      var type = typeof value;
      if (type == 'number' || type == 'symbol' || type == 'boolean' ||
          value == null || isSymbol(value)) {
        return true;
      }
      return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
        (object != null && value in Object(object));
    }

    /**
     * Checks if `value` is suitable for use as unique object key.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
     */
    function isKeyable(value) {
      var type = typeof value;
      return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
        ? (value !== '__proto__')
        : (value === null);
    }

    /**
     * Checks if `func` has a lazy counterpart.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
     *  else `false`.
     */
    function isLaziable(func) {
      var funcName = getFuncName(func),
          other = lodash[funcName];

      if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
        return false;
      }
      if (func === other) {
        return true;
      }
      var data = getData(other);
      return !!data && func === data[0];
    }

    /**
     * Checks if `func` has its source masked.
     *
     * @private
     * @param {Function} func The function to check.
     * @returns {boolean} Returns `true` if `func` is masked, else `false`.
     */
    function isMasked(func) {
      return !!maskSrcKey && (maskSrcKey in func);
    }

    /**
     * Checks if `func` is capable of being masked.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `func` is maskable, else `false`.
     */
    var isMaskable = coreJsData ? isFunction : stubFalse;

    /**
     * Checks if `value` is likely a prototype object.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
     */
    function isPrototype(value) {
      var Ctor = value && value.constructor,
          proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto;

      return value === proto;
    }

    /**
     * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` if suitable for strict
     *  equality comparisons, else `false`.
     */
    function isStrictComparable(value) {
      return value === value && !isObject(value);
    }

    /**
     * A specialized version of `matchesProperty` for source values suitable
     * for strict equality comparisons, i.e. `===`.
     *
     * @private
     * @param {string} key The key of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     */
    function matchesStrictComparable(key, srcValue) {
      return function(object) {
        if (object == null) {
          return false;
        }
        return object[key] === srcValue &&
          (srcValue !== undefined || (key in Object(object)));
      };
    }

    /**
     * A specialized version of `_.memoize` which clears the memoized function's
     * cache when it exceeds `MAX_MEMOIZE_SIZE`.
     *
     * @private
     * @param {Function} func The function to have its output memoized.
     * @returns {Function} Returns the new memoized function.
     */
    function memoizeCapped(func) {
      var result = memoize(func, function(key) {
        if (cache.size === MAX_MEMOIZE_SIZE) {
          cache.clear();
        }
        return key;
      });

      var cache = result.cache;
      return result;
    }

    /**
     * Merges the function metadata of `source` into `data`.
     *
     * Merging metadata reduces the number of wrappers used to invoke a function.
     * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
     * may be applied regardless of execution order. Methods like `_.ary` and
     * `_.rearg` modify function arguments, making the order in which they are
     * executed important, preventing the merging of metadata. However, we make
     * an exception for a safe combined case where curried functions have `_.ary`
     * and or `_.rearg` applied.
     *
     * @private
     * @param {Array} data The destination metadata.
     * @param {Array} source The source metadata.
     * @returns {Array} Returns `data`.
     */
    function mergeData(data, source) {
      var bitmask = data[1],
          srcBitmask = source[1],
          newBitmask = bitmask | srcBitmask,
          isCommon = newBitmask < (WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG | WRAP_ARY_FLAG);

      var isCombo =
        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_CURRY_FLAG)) ||
        ((srcBitmask == WRAP_ARY_FLAG) && (bitmask == WRAP_REARG_FLAG) && (data[7].length <= source[8])) ||
        ((srcBitmask == (WRAP_ARY_FLAG | WRAP_REARG_FLAG)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG));

      // Exit early if metadata can't be merged.
      if (!(isCommon || isCombo)) {
        return data;
      }
      // Use source `thisArg` if available.
      if (srcBitmask & WRAP_BIND_FLAG) {
        data[2] = source[2];
        // Set when currying a bound function.
        newBitmask |= bitmask & WRAP_BIND_FLAG ? 0 : WRAP_CURRY_BOUND_FLAG;
      }
      // Compose partial arguments.
      var value = source[3];
      if (value) {
        var partials = data[3];
        data[3] = partials ? composeArgs(partials, value, source[4]) : value;
        data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
      }
      // Compose partial right arguments.
      value = source[5];
      if (value) {
        partials = data[5];
        data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
        data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
      }
      // Use source `argPos` if available.
      value = source[7];
      if (value) {
        data[7] = value;
      }
      // Use source `ary` if it's smaller.
      if (srcBitmask & WRAP_ARY_FLAG) {
        data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
      }
      // Use source `arity` if one is not provided.
      if (data[9] == null) {
        data[9] = source[9];
      }
      // Use source `func` and merge bitmasks.
      data[0] = source[0];
      data[1] = newBitmask;

      return data;
    }

    /**
     * This function is like
     * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
     * except that it includes inherited enumerable properties.
     *
     * @private
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     */
    function nativeKeysIn(object) {
      var result = [];
      if (object != null) {
        for (var key in Object(object)) {
          result.push(key);
        }
      }
      return result;
    }

    /**
     * Converts `value` to a string using `Object.prototype.toString`.
     *
     * @private
     * @param {*} value The value to convert.
     * @returns {string} Returns the converted string.
     */
    function objectToString(value) {
      return nativeObjectToString.call(value);
    }

    /**
     * A specialized version of `baseRest` which transforms the rest array.
     *
     * @private
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @param {Function} transform The rest array transform.
     * @returns {Function} Returns the new function.
     */
    function overRest(func, start, transform) {
      start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
      return function() {
        var args = arguments,
            index = -1,
            length = nativeMax(args.length - start, 0),
            array = Array(length);

        while (++index < length) {
          array[index] = args[start + index];
        }
        index = -1;
        var otherArgs = Array(start + 1);
        while (++index < start) {
          otherArgs[index] = args[index];
        }
        otherArgs[start] = transform(array);
        return apply(func, this, otherArgs);
      };
    }

    /**
     * Gets the parent value at `path` of `object`.
     *
     * @private
     * @param {Object} object The object to query.
     * @param {Array} path The path to get the parent value of.
     * @returns {*} Returns the parent value.
     */
    function parent(object, path) {
      return path.length < 2 ? object : baseGet(object, baseSlice(path, 0, -1));
    }

    /**
     * Reorder `array` according to the specified indexes where the element at
     * the first index is assigned as the first element, the element at
     * the second index is assigned as the second element, and so on.
     *
     * @private
     * @param {Array} array The array to reorder.
     * @param {Array} indexes The arranged array indexes.
     * @returns {Array} Returns `array`.
     */
    function reorder(array, indexes) {
      var arrLength = array.length,
          length = nativeMin(indexes.length, arrLength),
          oldArray = copyArray(array);

      while (length--) {
        var index = indexes[length];
        array[length] = isIndex(index, arrLength) ? oldArray[index] : undefined;
      }
      return array;
    }

    /**
     * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
     *
     * @private
     * @param {Object} object The object to query.
     * @param {string} key The key of the property to get.
     * @returns {*} Returns the property value.
     */
    function safeGet(object, key) {
      if (key === 'constructor' && typeof object[key] === 'function') {
        return;
      }

      if (key == '__proto__') {
        return;
      }

      return object[key];
    }

    /**
     * Sets metadata for `func`.
     *
     * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
     * period of time, it will trip its breaker and transition to an identity
     * function to avoid garbage collection pauses in V8. See
     * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
     * for more details.
     *
     * @private
     * @param {Function} func The function to associate metadata with.
     * @param {*} data The metadata.
     * @returns {Function} Returns `func`.
     */
    var setData = shortOut(baseSetData);

    /**
     * A simple wrapper around the global [`setTimeout`](https://mdn.io/setTimeout).
     *
     * @private
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @returns {number|Object} Returns the timer id or timeout object.
     */
    var setTimeout = ctxSetTimeout || function(func, wait) {
      return root.setTimeout(func, wait);
    };

    /**
     * Sets the `toString` method of `func` to return `string`.
     *
     * @private
     * @param {Function} func The function to modify.
     * @param {Function} string The `toString` result.
     * @returns {Function} Returns `func`.
     */
    var setToString = shortOut(baseSetToString);

    /**
     * Sets the `toString` method of `wrapper` to mimic the source of `reference`
     * with wrapper details in a comment at the top of the source body.
     *
     * @private
     * @param {Function} wrapper The function to modify.
     * @param {Function} reference The reference function.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @returns {Function} Returns `wrapper`.
     */
    function setWrapToString(wrapper, reference, bitmask) {
      var source = (reference + '');
      return setToString(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
    }

    /**
     * Creates a function that'll short out and invoke `identity` instead
     * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
     * milliseconds.
     *
     * @private
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new shortable function.
     */
    function shortOut(func) {
      var count = 0,
          lastCalled = 0;

      return function() {
        var stamp = nativeNow(),
            remaining = HOT_SPAN - (stamp - lastCalled);

        lastCalled = stamp;
        if (remaining > 0) {
          if (++count >= HOT_COUNT) {
            return arguments[0];
          }
        } else {
          count = 0;
        }
        return func.apply(undefined, arguments);
      };
    }

    /**
     * A specialized version of `_.shuffle` which mutates and sets the size of `array`.
     *
     * @private
     * @param {Array} array The array to shuffle.
     * @param {number} [size=array.length] The size of `array`.
     * @returns {Array} Returns `array`.
     */
    function shuffleSelf(array, size) {
      var index = -1,
          length = array.length,
          lastIndex = length - 1;

      size = size === undefined ? length : size;
      while (++index < size) {
        var rand = baseRandom(index, lastIndex),
            value = array[rand];

        array[rand] = array[index];
        array[index] = value;
      }
      array.length = size;
      return array;
    }

    /**
     * Converts `string` to a property path array.
     *
     * @private
     * @param {string} string The string to convert.
     * @returns {Array} Returns the property path array.
     */
    var stringToPath = memoizeCapped(function(string) {
      var result = [];
      if (string.charCodeAt(0) === 46 /* . */) {
        result.push('');
      }
      string.replace(rePropName, function(match, number, quote, subString) {
        result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
      });
      return result;
    });

    /**
     * Converts `value` to a string key if it's not a string or symbol.
     *
     * @private
     * @param {*} value The value to inspect.
     * @returns {string|symbol} Returns the key.
     */
    function toKey(value) {
      if (typeof value == 'string' || isSymbol(value)) {
        return value;
      }
      var result = (value + '');
      return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
    }

    /**
     * Converts `func` to its source code.
     *
     * @private
     * @param {Function} func The function to convert.
     * @returns {string} Returns the source code.
     */
    function toSource(func) {
      if (func != null) {
        try {
          return funcToString.call(func);
        } catch (e) {}
        try {
          return (func + '');
        } catch (e) {}
      }
      return '';
    }

    /**
     * Updates wrapper `details` based on `bitmask` flags.
     *
     * @private
     * @returns {Array} details The details to modify.
     * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
     * @returns {Array} Returns `details`.
     */
    function updateWrapDetails(details, bitmask) {
      arrayEach(wrapFlags, function(pair) {
        var value = '_.' + pair[0];
        if ((bitmask & pair[1]) && !arrayIncludes(details, value)) {
          details.push(value);
        }
      });
      return details.sort();
    }

    /**
     * Creates a clone of `wrapper`.
     *
     * @private
     * @param {Object} wrapper The wrapper to clone.
     * @returns {Object} Returns the cloned wrapper.
     */
    function wrapperClone(wrapper) {
      if (wrapper instanceof LazyWrapper) {
        return wrapper.clone();
      }
      var result = new LodashWrapper(wrapper.__wrapped__, wrapper.__chain__);
      result.__actions__ = copyArray(wrapper.__actions__);
      result.__index__  = wrapper.__index__;
      result.__values__ = wrapper.__values__;
      return result;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an array of elements split into groups the length of `size`.
     * If `array` can't be split evenly, the final chunk will be the remaining
     * elements.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to process.
     * @param {number} [size=1] The length of each chunk
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the new array of chunks.
     * @example
     *
     * _.chunk(['a', 'b', 'c', 'd'], 2);
     * // => [['a', 'b'], ['c', 'd']]
     *
     * _.chunk(['a', 'b', 'c', 'd'], 3);
     * // => [['a', 'b', 'c'], ['d']]
     */
    function chunk(array, size, guard) {
      if ((guard ? isIterateeCall(array, size, guard) : size === undefined)) {
        size = 1;
      } else {
        size = nativeMax(toInteger(size), 0);
      }
      var length = array == null ? 0 : array.length;
      if (!length || size < 1) {
        return [];
      }
      var index = 0,
          resIndex = 0,
          result = Array(nativeCeil(length / size));

      while (index < length) {
        result[resIndex++] = baseSlice(array, index, (index += size));
      }
      return result;
    }

    /**
     * Creates an array with all falsey values removed. The values `false`, `null`,
     * `0`, `-0', '0n`, `""`, `undefined`, and `NaN` are falsy.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to compact.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.compact([0, 1, false, 2, '', 3]);
     * // => [1, 2, 3]
     */
    function compact(array) {
      var index = -1,
          length = array == null ? 0 : array.length,
          resIndex = 0,
          result = [];

      while (++index < length) {
        var value = array[index];
        if (value) {
          result[resIndex++] = value;
        }
      }
      return result;
    }

    /**
     * Creates a new array concatenating `array` with any additional arrays
     * and/or values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to concatenate.
     * @param {...*} [values] The values to concatenate.
     * @returns {Array} Returns the new concatenated array.
     * @example
     *
     * var array = [1];
     * var other = _.concat(array, 2, [3], [[4]]);
     *
     * console.log(other);
     * // => [1, 2, 3, [4]]
     *
     * console.log(array);
     * // => [1]
     */
    function concat() {
      var length = arguments.length;
      if (!length) {
        return [];
      }
      var args = Array(length - 1),
          array = arguments[0],
          index = length;

      while (index--) {
        args[index - 1] = arguments[index];
      }
      return arrayPush(isArray(array) ? copyArray(array) : [array], baseFlatten(args, 1));
    }

    /**
     * Creates an array of `array` values not included in the other given arrays
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. The order and references of result values are
     * determined by the first array.
     *
     * **Note:** Unlike `_.pullAll`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.without, _.xor
     * @example
     *
     * _.difference([2, 1], [2, 3]);
     * // => [1]
     */
    var difference = baseRest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `iteratee` which
     * is invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. The order and references of result values are
     * determined by the first array. The iteratee is invoked with one argument:
     * (value).
     *
     * **Note:** Unlike `_.pullAllBy`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.differenceBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.differenceBy([{ 'x': 2 }, { 'x': 1 }], [{ 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var differenceBy = baseRest(function(array, values) {
      var iteratee = last(values);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), getIteratee(iteratee, 2))
        : [];
    });

    /**
     * This method is like `_.difference` except that it accepts `comparator`
     * which is invoked to compare elements of `array` to `values`. The order and
     * references of result values are determined by the first array. The comparator
     * is invoked with two arguments: (arrVal, othVal).
     *
     * **Note:** Unlike `_.pullAllWith`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...Array} [values] The values to exclude.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     *
     * _.differenceWith(objects, [{ 'x': 1, 'y': 2 }], _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }]
     */
    var differenceWith = baseRest(function(array, values) {
      var comparator = last(values);
      if (isArrayLikeObject(comparator)) {
        comparator = undefined;
      }
      return isArrayLikeObject(array)
        ? baseDifference(array, baseFlatten(values, 1, isArrayLikeObject, true), undefined, comparator)
        : [];
    });

    /**
     * Creates a slice of `array` with `n` elements dropped from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.drop([1, 2, 3]);
     * // => [2, 3]
     *
     * _.drop([1, 2, 3], 2);
     * // => [3]
     *
     * _.drop([1, 2, 3], 5);
     * // => []
     *
     * _.drop([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function drop(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with `n` elements dropped from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to drop.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.dropRight([1, 2, 3]);
     * // => [1, 2]
     *
     * _.dropRight([1, 2, 3], 2);
     * // => [1]
     *
     * _.dropRight([1, 2, 3], 5);
     * // => []
     *
     * _.dropRight([1, 2, 3], 0);
     * // => [1, 2, 3]
     */
    function dropRight(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the end.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.dropRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropRightWhile(users, ['active', false]);
     * // => objects for ['barney']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropRightWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true, true)
        : [];
    }

    /**
     * Creates a slice of `array` excluding elements dropped from the beginning.
     * Elements are dropped until `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.dropWhile(users, function(o) { return !o.active; });
     * // => objects for ['pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.dropWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.dropWhile(users, ['active', false]);
     * // => objects for ['pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.dropWhile(users, 'active');
     * // => objects for ['barney', 'fred', 'pebbles']
     */
    function dropWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), true)
        : [];
    }

    /**
     * Fills elements of `array` with `value` from `start` up to, but not
     * including, `end`.
     *
     * **Note:** This method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Array
     * @param {Array} array The array to fill.
     * @param {*} value The value to fill `array` with.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.fill(array, 'a');
     * console.log(array);
     * // => ['a', 'a', 'a']
     *
     * _.fill(Array(3), 2);
     * // => [2, 2, 2]
     *
     * _.fill([4, 6, 8, 10], '*', 1, 3);
     * // => [4, '*', '*', 10]
     */
    function fill(array, value, start, end) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      if (start && typeof start != 'number' && isIterateeCall(array, value, start)) {
        start = 0;
        end = length;
      }
      return baseFill(array, value, start, end);
    }

    /**
     * This method is like `_.find` except that it returns the index of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.findIndex(users, function(o) { return o.user == 'barney'; });
     * // => 0
     *
     * // The `_.matches` iteratee shorthand.
     * _.findIndex(users, { 'user': 'fred', 'active': false });
     * // => 1
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findIndex(users, ['active', false]);
     * // => 0
     *
     * // The `_.property` iteratee shorthand.
     * _.findIndex(users, 'active');
     * // => 2
     */
    function findIndex(array, predicate, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = fromIndex == null ? 0 : toInteger(fromIndex);
      if (index < 0) {
        index = nativeMax(length + index, 0);
      }
      return baseFindIndex(array, getIteratee(predicate, 3), index);
    }

    /**
     * This method is like `_.findIndex` except that it iterates over elements
     * of `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=array.length-1] The index to search from.
     * @returns {number} Returns the index of the found element, else `-1`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.findLastIndex(users, function(o) { return o.user == 'pebbles'; });
     * // => 2
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastIndex(users, { 'user': 'barney', 'active': true });
     * // => 0
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastIndex(users, ['active', false]);
     * // => 2
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastIndex(users, 'active');
     * // => 0
     */
    function findLastIndex(array, predicate, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = length - 1;
      if (fromIndex !== undefined) {
        index = toInteger(fromIndex);
        index = fromIndex < 0
          ? nativeMax(length + index, 0)
          : nativeMin(index, length - 1);
      }
      return baseFindIndex(array, getIteratee(predicate, 3), index, true);
    }

    /**
     * Flattens `array` a single level deep.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flatten([1, [2, [3, [4]], 5]]);
     * // => [1, 2, [3, [4]], 5]
     */
    function flatten(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseFlatten(array, 1) : [];
    }

    /**
     * Recursively flattens `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * _.flattenDeep([1, [2, [3, [4]], 5]]);
     * // => [1, 2, 3, 4, 5]
     */
    function flattenDeep(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseFlatten(array, INFINITY) : [];
    }

    /**
     * Recursively flatten `array` up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Array
     * @param {Array} array The array to flatten.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * var array = [1, [2, [3, [4]], 5]];
     *
     * _.flattenDepth(array, 1);
     * // => [1, 2, [3, [4]], 5]
     *
     * _.flattenDepth(array, 2);
     * // => [1, 2, 3, [4], 5]
     */
    function flattenDepth(array, depth) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(array, depth);
    }

    /**
     * The inverse of `_.toPairs`; this method returns an object composed
     * from key-value `pairs`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} pairs The key-value pairs.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.fromPairs([['a', 1], ['b', 2]]);
     * // => { 'a': 1, 'b': 2 }
     */
    function fromPairs(pairs) {
      var index = -1,
          length = pairs == null ? 0 : pairs.length,
          result = {};

      while (++index < length) {
        var pair = pairs[index];
        result[pair[0]] = pair[1];
      }
      return result;
    }

    /**
     * Gets the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias first
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the first element of `array`.
     * @example
     *
     * _.head([1, 2, 3]);
     * // => 1
     *
     * _.head([]);
     * // => undefined
     */
    function head(array) {
      return (array && array.length) ? array[0] : undefined;
    }

    /**
     * Gets the index at which the first occurrence of `value` is found in `array`
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. If `fromIndex` is negative, it's used as the
     * offset from the end of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.indexOf([1, 2, 1, 2], 2);
     * // => 1
     *
     * // Search from the `fromIndex`.
     * _.indexOf([1, 2, 1, 2], 2, 2);
     * // => 3
     */
    function indexOf(array, value, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = fromIndex == null ? 0 : toInteger(fromIndex);
      if (index < 0) {
        index = nativeMax(length + index, 0);
      }
      return baseIndexOf(array, value, index);
    }

    /**
     * Gets all but the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.initial([1, 2, 3]);
     * // => [1, 2]
     */
    function initial(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseSlice(array, 0, -1) : [];
    }

    /**
     * Creates an array of unique values that are included in all given arrays
     * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons. The order and references of result values are
     * determined by the first array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersection([2, 1], [2, 3]);
     * // => [2]
     */
    var intersection = baseRest(function(arrays) {
      var mapped = arrayMap(arrays, castArrayLikeObject);
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped)
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `iteratee`
     * which is invoked for each element of each `arrays` to generate the criterion
     * by which they're compared. The order and references of result values are
     * determined by the first array. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * _.intersectionBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [2.1]
     *
     * // The `_.property` iteratee shorthand.
     * _.intersectionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }]
     */
    var intersectionBy = baseRest(function(arrays) {
      var iteratee = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      if (iteratee === last(mapped)) {
        iteratee = undefined;
      } else {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, getIteratee(iteratee, 2))
        : [];
    });

    /**
     * This method is like `_.intersection` except that it accepts `comparator`
     * which is invoked to compare elements of `arrays`. The order and references
     * of result values are determined by the first array. The comparator is
     * invoked with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of intersecting values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.intersectionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }]
     */
    var intersectionWith = baseRest(function(arrays) {
      var comparator = last(arrays),
          mapped = arrayMap(arrays, castArrayLikeObject);

      comparator = typeof comparator == 'function' ? comparator : undefined;
      if (comparator) {
        mapped.pop();
      }
      return (mapped.length && mapped[0] === arrays[0])
        ? baseIntersection(mapped, undefined, comparator)
        : [];
    });

    /**
     * Converts all elements in `array` into a string separated by `separator`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to convert.
     * @param {string} [separator=','] The element separator.
     * @returns {string} Returns the joined string.
     * @example
     *
     * _.join(['a', 'b', 'c'], '~');
     * // => 'a~b~c'
     */
    function join(array, separator) {
      return array == null ? '' : nativeJoin.call(array, separator);
    }

    /**
     * Gets the last element of `array`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {*} Returns the last element of `array`.
     * @example
     *
     * _.last([1, 2, 3]);
     * // => 3
     */
    function last(array) {
      var length = array == null ? 0 : array.length;
      return length ? array[length - 1] : undefined;
    }

    /**
     * This method is like `_.indexOf` except that it iterates over elements of
     * `array` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=array.length-1] The index to search from.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.lastIndexOf([1, 2, 1, 2], 2);
     * // => 3
     *
     * // Search from the `fromIndex`.
     * _.lastIndexOf([1, 2, 1, 2], 2, 2);
     * // => 1
     */
    function lastIndexOf(array, value, fromIndex) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return -1;
      }
      var index = length;
      if (fromIndex !== undefined) {
        index = toInteger(fromIndex);
        index = index < 0 ? nativeMax(length + index, 0) : nativeMin(index, length - 1);
      }
      return value === value
        ? strictLastIndexOf(array, value, index)
        : baseFindIndex(array, baseIsNaN, index, true);
    }

    /**
     * Gets the element at index `n` of `array`. If `n` is negative, the nth
     * element from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.11.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=0] The index of the element to return.
     * @returns {*} Returns the nth element of `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'd'];
     *
     * _.nth(array, 1);
     * // => 'b'
     *
     * _.nth(array, -2);
     * // => 'c';
     */
    function nth(array, n) {
      return (array && array.length) ? baseNth(array, toInteger(n)) : undefined;
    }

    /**
     * Removes all given values from `array` using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * **Note:** Unlike `_.without`, this method mutates `array`. Use `_.remove`
     * to remove elements from an array by predicate.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...*} [values] The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
     *
     * _.pull(array, 'a', 'c');
     * console.log(array);
     * // => ['b', 'b']
     */
    var pull = baseRest(pullAll);

    /**
     * This method is like `_.pull` except that it accepts an array of values to remove.
     *
     * **Note:** Unlike `_.difference`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = ['a', 'b', 'c', 'a', 'b', 'c'];
     *
     * _.pullAll(array, ['a', 'c']);
     * console.log(array);
     * // => ['b', 'b']
     */
    function pullAll(array, values) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values)
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `iteratee` which is
     * invoked for each element of `array` and `values` to generate the criterion
     * by which they're compared. The iteratee is invoked with one argument: (value).
     *
     * **Note:** Unlike `_.differenceBy`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1 }, { 'x': 2 }, { 'x': 3 }, { 'x': 1 }];
     *
     * _.pullAllBy(array, [{ 'x': 1 }, { 'x': 3 }], 'x');
     * console.log(array);
     * // => [{ 'x': 2 }]
     */
    function pullAllBy(array, values, iteratee) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, getIteratee(iteratee, 2))
        : array;
    }

    /**
     * This method is like `_.pullAll` except that it accepts `comparator` which
     * is invoked to compare elements of `array` to `values`. The comparator is
     * invoked with two arguments: (arrVal, othVal).
     *
     * **Note:** Unlike `_.differenceWith`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Array} values The values to remove.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [{ 'x': 1, 'y': 2 }, { 'x': 3, 'y': 4 }, { 'x': 5, 'y': 6 }];
     *
     * _.pullAllWith(array, [{ 'x': 3, 'y': 4 }], _.isEqual);
     * console.log(array);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 5, 'y': 6 }]
     */
    function pullAllWith(array, values, comparator) {
      return (array && array.length && values && values.length)
        ? basePullAll(array, values, undefined, comparator)
        : array;
    }

    /**
     * Removes elements from `array` corresponding to `indexes` and returns an
     * array of removed elements.
     *
     * **Note:** Unlike `_.at`, this method mutates `array`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {...(number|number[])} [indexes] The indexes of elements to remove.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = ['a', 'b', 'c', 'd'];
     * var pulled = _.pullAt(array, [1, 3]);
     *
     * console.log(array);
     * // => ['a', 'c']
     *
     * console.log(pulled);
     * // => ['b', 'd']
     */
    var pullAt = flatRest(function(array, indexes) {
      var length = array == null ? 0 : array.length,
          result = baseAt(array, indexes);

      basePullAt(array, arrayMap(indexes, function(index) {
        return isIndex(index, length) ? +index : index;
      }).sort(compareAscending));

      return result;
    });

    /**
     * Removes all elements from `array` that `predicate` returns truthy for
     * and returns an array of the removed elements. The predicate is invoked
     * with three arguments: (value, index, array).
     *
     * **Note:** Unlike `_.filter`, this method mutates `array`. Use `_.pull`
     * to pull elements from an array by value.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new array of removed elements.
     * @example
     *
     * var array = [1, 2, 3, 4];
     * var evens = _.remove(array, function(n) {
     *   return n % 2 == 0;
     * });
     *
     * console.log(array);
     * // => [1, 3]
     *
     * console.log(evens);
     * // => [2, 4]
     */
    function remove(array, predicate) {
      var result = [];
      if (!(array && array.length)) {
        return result;
      }
      var index = -1,
          indexes = [],
          length = array.length;

      predicate = getIteratee(predicate, 3);
      while (++index < length) {
        var value = array[index];
        if (predicate(value, index, array)) {
          result.push(value);
          indexes.push(index);
        }
      }
      basePullAt(array, indexes);
      return result;
    }

    /**
     * Reverses `array` so that the first element becomes the last, the second
     * element becomes the second to last, and so on.
     *
     * **Note:** This method mutates `array` and is based on
     * [`Array#reverse`](https://mdn.io/Array/reverse).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to modify.
     * @returns {Array} Returns `array`.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _.reverse(array);
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function reverse(array) {
      return array == null ? array : nativeReverse.call(array);
    }

    /**
     * Creates a slice of `array` from `start` up to, but not including, `end`.
     *
     * **Note:** This method is used instead of
     * [`Array#slice`](https://mdn.io/Array/slice) to ensure dense arrays are
     * returned.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to slice.
     * @param {number} [start=0] The start position.
     * @param {number} [end=array.length] The end position.
     * @returns {Array} Returns the slice of `array`.
     */
    function slice(array, start, end) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      if (end && typeof end != 'number' && isIterateeCall(array, start, end)) {
        start = 0;
        end = length;
      }
      else {
        start = start == null ? 0 : toInteger(start);
        end = end === undefined ? length : toInteger(end);
      }
      return baseSlice(array, start, end);
    }

    /**
     * Uses a binary search to determine the lowest index at which `value`
     * should be inserted into `array` in order to maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedIndex([30, 50], 40);
     * // => 1
     */
    function sortedIndex(array, value) {
      return baseSortedIndex(array, value);
    }

    /**
     * This method is like `_.sortedIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * var objects = [{ 'x': 4 }, { 'x': 5 }];
     *
     * _.sortedIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
     * // => 0
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedIndexBy(objects, { 'x': 4 }, 'x');
     * // => 0
     */
    function sortedIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2));
    }

    /**
     * This method is like `_.indexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedIndexOf([4, 5, 5, 5, 6], 5);
     * // => 1
     */
    function sortedIndexOf(array, value) {
      var length = array == null ? 0 : array.length;
      if (length) {
        var index = baseSortedIndex(array, value);
        if (index < length && eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.sortedIndex` except that it returns the highest
     * index at which `value` should be inserted into `array` in order to
     * maintain its sort order.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * _.sortedLastIndex([4, 5, 5, 5, 6], 5);
     * // => 4
     */
    function sortedLastIndex(array, value) {
      return baseSortedIndex(array, value, true);
    }

    /**
     * This method is like `_.sortedLastIndex` except that it accepts `iteratee`
     * which is invoked for `value` and each element of `array` to compute their
     * sort ranking. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The sorted array to inspect.
     * @param {*} value The value to evaluate.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the index at which `value` should be inserted
     *  into `array`.
     * @example
     *
     * var objects = [{ 'x': 4 }, { 'x': 5 }];
     *
     * _.sortedLastIndexBy(objects, { 'x': 4 }, function(o) { return o.x; });
     * // => 1
     *
     * // The `_.property` iteratee shorthand.
     * _.sortedLastIndexBy(objects, { 'x': 4 }, 'x');
     * // => 1
     */
    function sortedLastIndexBy(array, value, iteratee) {
      return baseSortedIndexBy(array, value, getIteratee(iteratee, 2), true);
    }

    /**
     * This method is like `_.lastIndexOf` except that it performs a binary
     * search on a sorted `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {*} value The value to search for.
     * @returns {number} Returns the index of the matched value, else `-1`.
     * @example
     *
     * _.sortedLastIndexOf([4, 5, 5, 5, 6], 5);
     * // => 3
     */
    function sortedLastIndexOf(array, value) {
      var length = array == null ? 0 : array.length;
      if (length) {
        var index = baseSortedIndex(array, value, true) - 1;
        if (eq(array[index], value)) {
          return index;
        }
      }
      return -1;
    }

    /**
     * This method is like `_.uniq` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniq([1, 1, 2]);
     * // => [1, 2]
     */
    function sortedUniq(array) {
      return (array && array.length)
        ? baseSortedUniq(array)
        : [];
    }

    /**
     * This method is like `_.uniqBy` except that it's designed and optimized
     * for sorted arrays.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.sortedUniqBy([1.1, 1.2, 2.3, 2.4], Math.floor);
     * // => [1.1, 2.3]
     */
    function sortedUniqBy(array, iteratee) {
      return (array && array.length)
        ? baseSortedUniq(array, getIteratee(iteratee, 2))
        : [];
    }

    /**
     * Gets all but the first element of `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.tail([1, 2, 3]);
     * // => [2, 3]
     */
    function tail(array) {
      var length = array == null ? 0 : array.length;
      return length ? baseSlice(array, 1, length) : [];
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the beginning.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.take([1, 2, 3]);
     * // => [1]
     *
     * _.take([1, 2, 3], 2);
     * // => [1, 2]
     *
     * _.take([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.take([1, 2, 3], 0);
     * // => []
     */
    function take(array, n, guard) {
      if (!(array && array.length)) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      return baseSlice(array, 0, n < 0 ? 0 : n);
    }

    /**
     * Creates a slice of `array` with `n` elements taken from the end.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {number} [n=1] The number of elements to take.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * _.takeRight([1, 2, 3]);
     * // => [3]
     *
     * _.takeRight([1, 2, 3], 2);
     * // => [2, 3]
     *
     * _.takeRight([1, 2, 3], 5);
     * // => [1, 2, 3]
     *
     * _.takeRight([1, 2, 3], 0);
     * // => []
     */
    function takeRight(array, n, guard) {
      var length = array == null ? 0 : array.length;
      if (!length) {
        return [];
      }
      n = (guard || n === undefined) ? 1 : toInteger(n);
      n = length - n;
      return baseSlice(array, n < 0 ? 0 : n, length);
    }

    /**
     * Creates a slice of `array` with elements taken from the end. Elements are
     * taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': true },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': false }
     * ];
     *
     * _.takeRightWhile(users, function(o) { return !o.active; });
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeRightWhile(users, { 'user': 'pebbles', 'active': false });
     * // => objects for ['pebbles']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeRightWhile(users, ['active', false]);
     * // => objects for ['fred', 'pebbles']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeRightWhile(users, 'active');
     * // => []
     */
    function takeRightWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3), false, true)
        : [];
    }

    /**
     * Creates a slice of `array` with elements taken from the beginning. Elements
     * are taken until `predicate` returns falsey. The predicate is invoked with
     * three arguments: (value, index, array).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Array
     * @param {Array} array The array to query.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the slice of `array`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'active': false },
     *   { 'user': 'fred',    'active': false },
     *   { 'user': 'pebbles', 'active': true }
     * ];
     *
     * _.takeWhile(users, function(o) { return !o.active; });
     * // => objects for ['barney', 'fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.takeWhile(users, { 'user': 'barney', 'active': false });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.takeWhile(users, ['active', false]);
     * // => objects for ['barney', 'fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.takeWhile(users, 'active');
     * // => []
     */
    function takeWhile(array, predicate) {
      return (array && array.length)
        ? baseWhile(array, getIteratee(predicate, 3))
        : [];
    }

    /**
     * Creates an array of unique values, in order, from all given arrays using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.union([2], [1, 2]);
     * // => [2, 1]
     */
    var union = baseRest(function(arrays) {
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true));
    });

    /**
     * This method is like `_.union` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which uniqueness is computed. Result values are chosen from the first
     * array in which the value occurs. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * _.unionBy([2.1], [1.2, 2.3], Math.floor);
     * // => [2.1, 1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.unionBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    var unionBy = baseRest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), getIteratee(iteratee, 2));
    });

    /**
     * This method is like `_.union` except that it accepts `comparator` which
     * is invoked to compare elements of `arrays`. Result values are chosen from
     * the first array in which the value occurs. The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of combined values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.unionWith(objects, others, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var unionWith = baseRest(function(arrays) {
      var comparator = last(arrays);
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return baseUniq(baseFlatten(arrays, 1, isArrayLikeObject, true), undefined, comparator);
    });

    /**
     * Creates a duplicate-free version of an array, using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons, in which only the first occurrence of each element
     * is kept. The order of result values is determined by the order they occur
     * in the array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniq([2, 1, 2]);
     * // => [2, 1]
     */
    function uniq(array) {
      return (array && array.length) ? baseUniq(array) : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * uniqueness is computed. The order of result values is determined by the
     * order they occur in the array. The iteratee is invoked with one argument:
     * (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
     * // => [2.1, 1.2]
     *
     * // The `_.property` iteratee shorthand.
     * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 1 }, { 'x': 2 }]
     */
    function uniqBy(array, iteratee) {
      return (array && array.length) ? baseUniq(array, getIteratee(iteratee, 2)) : [];
    }

    /**
     * This method is like `_.uniq` except that it accepts `comparator` which
     * is invoked to compare elements of `array`. The order of result values is
     * determined by the order they occur in the array.The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new duplicate free array.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.uniqWith(objects, _.isEqual);
     * // => [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }]
     */
    function uniqWith(array, comparator) {
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return (array && array.length) ? baseUniq(array, undefined, comparator) : [];
    }

    /**
     * This method is like `_.zip` except that it accepts an array of grouped
     * elements and creates an array regrouping the elements to their pre-zip
     * configuration.
     *
     * @static
     * @memberOf _
     * @since 1.2.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip(['a', 'b'], [1, 2], [true, false]);
     * // => [['a', 1, true], ['b', 2, false]]
     *
     * _.unzip(zipped);
     * // => [['a', 'b'], [1, 2], [true, false]]
     */
    function unzip(array) {
      if (!(array && array.length)) {
        return [];
      }
      var length = 0;
      array = arrayFilter(array, function(group) {
        if (isArrayLikeObject(group)) {
          length = nativeMax(group.length, length);
          return true;
        }
      });
      return baseTimes(length, function(index) {
        return arrayMap(array, baseProperty(index));
      });
    }

    /**
     * This method is like `_.unzip` except that it accepts `iteratee` to specify
     * how regrouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {Array} array The array of grouped elements to process.
     * @param {Function} [iteratee=_.identity] The function to combine
     *  regrouped values.
     * @returns {Array} Returns the new array of regrouped elements.
     * @example
     *
     * var zipped = _.zip([1, 2], [10, 20], [100, 200]);
     * // => [[1, 10, 100], [2, 20, 200]]
     *
     * _.unzipWith(zipped, _.add);
     * // => [3, 30, 300]
     */
    function unzipWith(array, iteratee) {
      if (!(array && array.length)) {
        return [];
      }
      var result = unzip(array);
      if (iteratee == null) {
        return result;
      }
      return arrayMap(result, function(group) {
        return apply(iteratee, undefined, group);
      });
    }

    /**
     * Creates an array excluding all given values using
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * for equality comparisons.
     *
     * **Note:** Unlike `_.pull`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {Array} array The array to inspect.
     * @param {...*} [values] The values to exclude.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.xor
     * @example
     *
     * _.without([2, 1, 2, 3], 1, 2);
     * // => [3]
     */
    var without = baseRest(function(array, values) {
      return isArrayLikeObject(array)
        ? baseDifference(array, values)
        : [];
    });

    /**
     * Creates an array of unique values that is the
     * [symmetric difference](https://en.wikipedia.org/wiki/Symmetric_difference)
     * of the given arrays. The order of result values is determined by the order
     * they occur in the arrays.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @returns {Array} Returns the new array of filtered values.
     * @see _.difference, _.without
     * @example
     *
     * _.xor([2, 1], [2, 3]);
     * // => [1, 3]
     */
    var xor = baseRest(function(arrays) {
      return baseXor(arrayFilter(arrays, isArrayLikeObject));
    });

    /**
     * This method is like `_.xor` except that it accepts `iteratee` which is
     * invoked for each element of each `arrays` to generate the criterion by
     * which by which they're compared. The order of result values is determined
     * by the order they occur in the arrays. The iteratee is invoked with one
     * argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * _.xorBy([2.1, 1.2], [2.3, 3.4], Math.floor);
     * // => [1.2, 3.4]
     *
     * // The `_.property` iteratee shorthand.
     * _.xorBy([{ 'x': 1 }], [{ 'x': 2 }, { 'x': 1 }], 'x');
     * // => [{ 'x': 2 }]
     */
    var xorBy = baseRest(function(arrays) {
      var iteratee = last(arrays);
      if (isArrayLikeObject(iteratee)) {
        iteratee = undefined;
      }
      return baseXor(arrayFilter(arrays, isArrayLikeObject), getIteratee(iteratee, 2));
    });

    /**
     * This method is like `_.xor` except that it accepts `comparator` which is
     * invoked to compare elements of `arrays`. The order of result values is
     * determined by the order they occur in the arrays. The comparator is invoked
     * with two arguments: (arrVal, othVal).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Array
     * @param {...Array} [arrays] The arrays to inspect.
     * @param {Function} [comparator] The comparator invoked per element.
     * @returns {Array} Returns the new array of filtered values.
     * @example
     *
     * var objects = [{ 'x': 1, 'y': 2 }, { 'x': 2, 'y': 1 }];
     * var others = [{ 'x': 1, 'y': 1 }, { 'x': 1, 'y': 2 }];
     *
     * _.xorWith(objects, others, _.isEqual);
     * // => [{ 'x': 2, 'y': 1 }, { 'x': 1, 'y': 1 }]
     */
    var xorWith = baseRest(function(arrays) {
      var comparator = last(arrays);
      comparator = typeof comparator == 'function' ? comparator : undefined;
      return baseXor(arrayFilter(arrays, isArrayLikeObject), undefined, comparator);
    });

    /**
     * Creates an array of grouped elements, the first of which contains the
     * first elements of the given arrays, the second of which contains the
     * second elements of the given arrays, and so on.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zip(['a', 'b'], [1, 2], [true, false]);
     * // => [['a', 1, true], ['b', 2, false]]
     */
    var zip = baseRest(unzip);

    /**
     * This method is like `_.fromPairs` except that it accepts two arrays,
     * one of property identifiers and one of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 0.4.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObject(['a', 'b'], [1, 2]);
     * // => { 'a': 1, 'b': 2 }
     */
    function zipObject(props, values) {
      return baseZipObject(props || [], values || [], assignValue);
    }

    /**
     * This method is like `_.zipObject` except that it supports property paths.
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Array
     * @param {Array} [props=[]] The property identifiers.
     * @param {Array} [values=[]] The property values.
     * @returns {Object} Returns the new object.
     * @example
     *
     * _.zipObjectDeep(['a.b[0].c', 'a.b[1].d'], [1, 2]);
     * // => { 'a': { 'b': [{ 'c': 1 }, { 'd': 2 }] } }
     */
    function zipObjectDeep(props, values) {
      return baseZipObject(props || [], values || [], baseSet);
    }

    /**
     * This method is like `_.zip` except that it accepts `iteratee` to specify
     * how grouped values should be combined. The iteratee is invoked with the
     * elements of each group: (...group).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Array
     * @param {...Array} [arrays] The arrays to process.
     * @param {Function} [iteratee=_.identity] The function to combine
     *  grouped values.
     * @returns {Array} Returns the new array of grouped elements.
     * @example
     *
     * _.zipWith([1, 2], [10, 20], [100, 200], function(a, b, c) {
     *   return a + b + c;
     * });
     * // => [111, 222]
     */
    var zipWith = baseRest(function(arrays) {
      var length = arrays.length,
          iteratee = length > 1 ? arrays[length - 1] : undefined;

      iteratee = typeof iteratee == 'function' ? (arrays.pop(), iteratee) : undefined;
      return unzipWith(arrays, iteratee);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Creates a `lodash` wrapper instance that wraps `value` with explicit method
     * chain sequences enabled. The result of such sequences must be unwrapped
     * with `_#value`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Seq
     * @param {*} value The value to wrap.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36 },
     *   { 'user': 'fred',    'age': 40 },
     *   { 'user': 'pebbles', 'age': 1 }
     * ];
     *
     * var youngest = _
     *   .chain(users)
     *   .sortBy('age')
     *   .map(function(o) {
     *     return o.user + ' is ' + o.age;
     *   })
     *   .head()
     *   .value();
     * // => 'pebbles is 1'
     */
    function chain(value) {
      var result = lodash(value);
      result.__chain__ = true;
      return result;
    }

    /**
     * This method invokes `interceptor` and returns `value`. The interceptor
     * is invoked with one argument; (value). The purpose of this method is to
     * "tap into" a method chain sequence in order to modify intermediate results.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns `value`.
     * @example
     *
     * _([1, 2, 3])
     *  .tap(function(array) {
     *    // Mutate input array.
     *    array.pop();
     *  })
     *  .reverse()
     *  .value();
     * // => [2, 1]
     */
    function tap(value, interceptor) {
      interceptor(value);
      return value;
    }

    /**
     * This method is like `_.tap` except that it returns the result of `interceptor`.
     * The purpose of this method is to "pass thru" values replacing intermediate
     * results in a method chain sequence.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Seq
     * @param {*} value The value to provide to `interceptor`.
     * @param {Function} interceptor The function to invoke.
     * @returns {*} Returns the result of `interceptor`.
     * @example
     *
     * _('  abc  ')
     *  .chain()
     *  .trim()
     *  .thru(function(value) {
     *    return [value];
     *  })
     *  .value();
     * // => ['abc']
     */
    function thru(value, interceptor) {
      return interceptor(value);
    }

    /**
     * This method is the wrapper version of `_.at`.
     *
     * @name at
     * @memberOf _
     * @since 1.0.0
     * @category Seq
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _(object).at(['a[0].b.c', 'a[1]']).value();
     * // => [3, 4]
     */
    var wrapperAt = flatRest(function(paths) {
      var length = paths.length,
          start = length ? paths[0] : 0,
          value = this.__wrapped__,
          interceptor = function(object) { return baseAt(object, paths); };

      if (length > 1 || this.__actions__.length ||
          !(value instanceof LazyWrapper) || !isIndex(start)) {
        return this.thru(interceptor);
      }
      value = value.slice(start, +start + (length ? 1 : 0));
      value.__actions__.push({
        'func': thru,
        'args': [interceptor],
        'thisArg': undefined
      });
      return new LodashWrapper(value, this.__chain__).thru(function(array) {
        if (length && !array.length) {
          array.push(undefined);
        }
        return array;
      });
    });

    /**
     * Creates a `lodash` wrapper instance with explicit method chain sequences enabled.
     *
     * @name chain
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 40 }
     * ];
     *
     * // A sequence without explicit chaining.
     * _(users).head();
     * // => { 'user': 'barney', 'age': 36 }
     *
     * // A sequence with explicit chaining.
     * _(users)
     *   .chain()
     *   .head()
     *   .pick('user')
     *   .value();
     * // => { 'user': 'barney' }
     */
    function wrapperChain() {
      return chain(this);
    }

    /**
     * Executes the chain sequence and returns the wrapped result.
     *
     * @name commit
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2];
     * var wrapped = _(array).push(3);
     *
     * console.log(array);
     * // => [1, 2]
     *
     * wrapped = wrapped.commit();
     * console.log(array);
     * // => [1, 2, 3]
     *
     * wrapped.last();
     * // => 3
     *
     * console.log(array);
     * // => [1, 2, 3]
     */
    function wrapperCommit() {
      return new LodashWrapper(this.value(), this.__chain__);
    }

    /**
     * Gets the next value on a wrapped object following the
     * [iterator protocol](https://mdn.io/iteration_protocols#iterator).
     *
     * @name next
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the next iterator value.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 1 }
     *
     * wrapped.next();
     * // => { 'done': false, 'value': 2 }
     *
     * wrapped.next();
     * // => { 'done': true, 'value': undefined }
     */
    function wrapperNext() {
      if (this.__values__ === undefined) {
        this.__values__ = toArray(this.value());
      }
      var done = this.__index__ >= this.__values__.length,
          value = done ? undefined : this.__values__[this.__index__++];

      return { 'done': done, 'value': value };
    }

    /**
     * Enables the wrapper to be iterable.
     *
     * @name Symbol.iterator
     * @memberOf _
     * @since 4.0.0
     * @category Seq
     * @returns {Object} Returns the wrapper object.
     * @example
     *
     * var wrapped = _([1, 2]);
     *
     * wrapped[Symbol.iterator]() === wrapped;
     * // => true
     *
     * Array.from(wrapped);
     * // => [1, 2]
     */
    function wrapperToIterator() {
      return this;
    }

    /**
     * Creates a clone of the chain sequence planting `value` as the wrapped value.
     *
     * @name plant
     * @memberOf _
     * @since 3.2.0
     * @category Seq
     * @param {*} value The value to plant.
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var wrapped = _([1, 2]).map(square);
     * var other = wrapped.plant([3, 4]);
     *
     * other.value();
     * // => [9, 16]
     *
     * wrapped.value();
     * // => [1, 4]
     */
    function wrapperPlant(value) {
      var result,
          parent = this;

      while (parent instanceof baseLodash) {
        var clone = wrapperClone(parent);
        clone.__index__ = 0;
        clone.__values__ = undefined;
        if (result) {
          previous.__wrapped__ = clone;
        } else {
          result = clone;
        }
        var previous = clone;
        parent = parent.__wrapped__;
      }
      previous.__wrapped__ = value;
      return result;
    }

    /**
     * This method is the wrapper version of `_.reverse`.
     *
     * **Note:** This method mutates the wrapped array.
     *
     * @name reverse
     * @memberOf _
     * @since 0.1.0
     * @category Seq
     * @returns {Object} Returns the new `lodash` wrapper instance.
     * @example
     *
     * var array = [1, 2, 3];
     *
     * _(array).reverse().value()
     * // => [3, 2, 1]
     *
     * console.log(array);
     * // => [3, 2, 1]
     */
    function wrapperReverse() {
      var value = this.__wrapped__;
      if (value instanceof LazyWrapper) {
        var wrapped = value;
        if (this.__actions__.length) {
          wrapped = new LazyWrapper(this);
        }
        wrapped = wrapped.reverse();
        wrapped.__actions__.push({
          'func': thru,
          'args': [reverse],
          'thisArg': undefined
        });
        return new LodashWrapper(wrapped, this.__chain__);
      }
      return this.thru(reverse);
    }

    /**
     * Executes the chain sequence to resolve the unwrapped value.
     *
     * @name value
     * @memberOf _
     * @since 0.1.0
     * @alias toJSON, valueOf
     * @category Seq
     * @returns {*} Returns the resolved unwrapped value.
     * @example
     *
     * _([1, 2, 3]).value();
     * // => [1, 2, 3]
     */
    function wrapperValue() {
      return baseWrapperValue(this.__wrapped__, this.__actions__);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the number of times the key was returned by `iteratee`. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.countBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': 1, '6': 2 }
     *
     * // The `_.property` iteratee shorthand.
     * _.countBy(['one', 'two', 'three'], 'length');
     * // => { '3': 2, '5': 1 }
     */
    var countBy = createAggregator(function(result, value, key) {
      if (hasOwnProperty.call(result, key)) {
        ++result[key];
      } else {
        baseAssignValue(result, key, 1);
      }
    });

    /**
     * Checks if `predicate` returns truthy for **all** elements of `collection`.
     * Iteration is stopped once `predicate` returns falsey. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * **Note:** This method returns `true` for
     * [empty collections](https://en.wikipedia.org/wiki/Empty_set) because
     * [everything is true](https://en.wikipedia.org/wiki/Vacuous_truth) of
     * elements of empty collections.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if all elements pass the predicate check,
     *  else `false`.
     * @example
     *
     * _.every([true, 1, null, 'yes'], Boolean);
     * // => false
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.every(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.every(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.every(users, 'active');
     * // => false
     */
    function every(collection, predicate, guard) {
      var func = isArray(collection) ? arrayEvery : baseEvery;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning an array of all elements
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * **Note:** Unlike `_.remove`, this method returns a new array.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.reject
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * _.filter(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, { 'age': 36, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.filter(users, 'active');
     * // => objects for ['barney']
     *
     * // Combining several predicates using `_.overEvery` or `_.overSome`.
     * _.filter(users, _.overSome([{ 'age': 36 }, ['age', 40]]));
     * // => objects for ['fred', 'barney']
     */
    function filter(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Iterates over elements of `collection`, returning the first element
     * `predicate` returns truthy for. The predicate is invoked with three
     * arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=0] The index to search from.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': true },
     *   { 'user': 'fred',    'age': 40, 'active': false },
     *   { 'user': 'pebbles', 'age': 1,  'active': true }
     * ];
     *
     * _.find(users, function(o) { return o.age < 40; });
     * // => object for 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.find(users, { 'age': 1, 'active': true });
     * // => object for 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.find(users, ['active', false]);
     * // => object for 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.find(users, 'active');
     * // => object for 'barney'
     */
    var find = createFind(findIndex);

    /**
     * This method is like `_.find` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param {number} [fromIndex=collection.length-1] The index to search from.
     * @returns {*} Returns the matched element, else `undefined`.
     * @example
     *
     * _.findLast([1, 2, 3, 4], function(n) {
     *   return n % 2 == 1;
     * });
     * // => 3
     */
    var findLast = createFind(findLastIndex);

    /**
     * Creates a flattened array of values by running each element in `collection`
     * thru `iteratee` and flattening the mapped results. The iteratee is invoked
     * with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [n, n];
     * }
     *
     * _.flatMap([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMap(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), 1);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDeep([1, 2], duplicate);
     * // => [1, 1, 2, 2]
     */
    function flatMapDeep(collection, iteratee) {
      return baseFlatten(map(collection, iteratee), INFINITY);
    }

    /**
     * This method is like `_.flatMap` except that it recursively flattens the
     * mapped results up to `depth` times.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {number} [depth=1] The maximum recursion depth.
     * @returns {Array} Returns the new flattened array.
     * @example
     *
     * function duplicate(n) {
     *   return [[[n, n]]];
     * }
     *
     * _.flatMapDepth([1, 2], duplicate, 2);
     * // => [[1, 1], [2, 2]]
     */
    function flatMapDepth(collection, iteratee, depth) {
      depth = depth === undefined ? 1 : toInteger(depth);
      return baseFlatten(map(collection, iteratee), depth);
    }

    /**
     * Iterates over elements of `collection` and invokes `iteratee` for each element.
     * The iteratee is invoked with three arguments: (value, index|key, collection).
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * **Note:** As with other "Collections" methods, objects with a "length"
     * property are iterated like arrays. To avoid this behavior use `_.forIn`
     * or `_.forOwn` for object iteration.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @alias each
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEachRight
     * @example
     *
     * _.forEach([1, 2], function(value) {
     *   console.log(value);
     * });
     * // => Logs `1` then `2`.
     *
     * _.forEach({ 'a': 1, 'b': 2 }, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forEach(collection, iteratee) {
      var func = isArray(collection) ? arrayEach : baseEach;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forEach` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @alias eachRight
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array|Object} Returns `collection`.
     * @see _.forEach
     * @example
     *
     * _.forEachRight([1, 2], function(value) {
     *   console.log(value);
     * });
     * // => Logs `2` then `1`.
     */
    function forEachRight(collection, iteratee) {
      var func = isArray(collection) ? arrayEachRight : baseEachRight;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The order of grouped values
     * is determined by the order they occur in `collection`. The corresponding
     * value of each key is an array of elements responsible for generating the
     * key. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * _.groupBy([6.1, 4.2, 6.3], Math.floor);
     * // => { '4': [4.2], '6': [6.1, 6.3] }
     *
     * // The `_.property` iteratee shorthand.
     * _.groupBy(['one', 'two', 'three'], 'length');
     * // => { '3': ['one', 'two'], '5': ['three'] }
     */
    var groupBy = createAggregator(function(result, value, key) {
      if (hasOwnProperty.call(result, key)) {
        result[key].push(value);
      } else {
        baseAssignValue(result, key, [value]);
      }
    });

    /**
     * Checks if `value` is in `collection`. If `collection` is a string, it's
     * checked for a substring of `value`, otherwise
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * is used for equality comparisons. If `fromIndex` is negative, it's used as
     * the offset from the end of `collection`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object|string} collection The collection to inspect.
     * @param {*} value The value to search for.
     * @param {number} [fromIndex=0] The index to search from.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {boolean} Returns `true` if `value` is found, else `false`.
     * @example
     *
     * _.includes([1, 2, 3], 1);
     * // => true
     *
     * _.includes([1, 2, 3], 1, 2);
     * // => false
     *
     * _.includes({ 'a': 1, 'b': 2 }, 1);
     * // => true
     *
     * _.includes('abcd', 'bc');
     * // => true
     */
    function includes(collection, value, fromIndex, guard) {
      collection = isArrayLike(collection) ? collection : values(collection);
      fromIndex = (fromIndex && !guard) ? toInteger(fromIndex) : 0;

      var length = collection.length;
      if (fromIndex < 0) {
        fromIndex = nativeMax(length + fromIndex, 0);
      }
      return isString(collection)
        ? (fromIndex <= length && collection.indexOf(value, fromIndex) > -1)
        : (!!length && baseIndexOf(collection, value, fromIndex) > -1);
    }

    /**
     * Invokes the method at `path` of each element in `collection`, returning
     * an array of the results of each invoked method. Any additional arguments
     * are provided to each invoked method. If `path` is a function, it's invoked
     * for, and `this` bound to, each element in `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array|Function|string} path The path of the method to invoke or
     *  the function invoked per iteration.
     * @param {...*} [args] The arguments to invoke each method with.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.invokeMap([[5, 1, 7], [3, 2, 1]], 'sort');
     * // => [[1, 5, 7], [1, 2, 3]]
     *
     * _.invokeMap([123, 456], String.prototype.split, '');
     * // => [['1', '2', '3'], ['4', '5', '6']]
     */
    var invokeMap = baseRest(function(collection, path, args) {
      var index = -1,
          isFunc = typeof path == 'function',
          result = isArrayLike(collection) ? Array(collection.length) : [];

      baseEach(collection, function(value) {
        result[++index] = isFunc ? apply(path, value, args) : baseInvoke(value, path, args);
      });
      return result;
    });

    /**
     * Creates an object composed of keys generated from the results of running
     * each element of `collection` thru `iteratee`. The corresponding value of
     * each key is the last element responsible for generating the key. The
     * iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee to transform keys.
     * @returns {Object} Returns the composed aggregate object.
     * @example
     *
     * var array = [
     *   { 'dir': 'left', 'code': 97 },
     *   { 'dir': 'right', 'code': 100 }
     * ];
     *
     * _.keyBy(array, function(o) {
     *   return String.fromCharCode(o.code);
     * });
     * // => { 'a': { 'dir': 'left', 'code': 97 }, 'd': { 'dir': 'right', 'code': 100 } }
     *
     * _.keyBy(array, 'dir');
     * // => { 'left': { 'dir': 'left', 'code': 97 }, 'right': { 'dir': 'right', 'code': 100 } }
     */
    var keyBy = createAggregator(function(result, value, key) {
      baseAssignValue(result, key, value);
    });

    /**
     * Creates an array of values by running each element in `collection` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.every`, `_.filter`, `_.map`, `_.mapValues`, `_.reject`, and `_.some`.
     *
     * The guarded methods are:
     * `ary`, `chunk`, `curry`, `curryRight`, `drop`, `dropRight`, `every`,
     * `fill`, `invert`, `parseInt`, `random`, `range`, `rangeRight`, `repeat`,
     * `sampleSize`, `slice`, `some`, `sortBy`, `split`, `take`, `takeRight`,
     * `template`, `trim`, `trimEnd`, `trimStart`, and `words`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new mapped array.
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * _.map([4, 8], square);
     * // => [16, 64]
     *
     * _.map({ 'a': 4, 'b': 8 }, square);
     * // => [16, 64] (iteration order is not guaranteed)
     *
     * var users = [
     *   { 'user': 'barney' },
     *   { 'user': 'fred' }
     * ];
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, 'user');
     * // => ['barney', 'fred']
     */
    function map(collection, iteratee) {
      var func = isArray(collection) ? arrayMap : baseMap;
      return func(collection, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.sortBy` except that it allows specifying the sort
     * orders of the iteratees to sort by. If `orders` is unspecified, all values
     * are sorted in ascending order. Otherwise, specify an order of "desc" for
     * descending or "asc" for ascending sort order of corresponding values.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Array[]|Function[]|Object[]|string[]} [iteratees=[_.identity]]
     *  The iteratees to sort by.
     * @param {string[]} [orders] The sort orders of `iteratees`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.reduce`.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 34 },
     *   { 'user': 'fred',   'age': 40 },
     *   { 'user': 'barney', 'age': 36 }
     * ];
     *
     * // Sort by `user` in ascending order and by `age` in descending order.
     * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 40]]
     */
    function orderBy(collection, iteratees, orders, guard) {
      if (collection == null) {
        return [];
      }
      if (!isArray(iteratees)) {
        iteratees = iteratees == null ? [] : [iteratees];
      }
      orders = guard ? undefined : orders;
      if (!isArray(orders)) {
        orders = orders == null ? [] : [orders];
      }
      return baseOrderBy(collection, iteratees, orders);
    }

    /**
     * Creates an array of elements split into two groups, the first of which
     * contains elements `predicate` returns truthy for, the second of which
     * contains elements `predicate` returns falsey for. The predicate is
     * invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the array of grouped elements.
     * @example
     *
     * var users = [
     *   { 'user': 'barney',  'age': 36, 'active': false },
     *   { 'user': 'fred',    'age': 40, 'active': true },
     *   { 'user': 'pebbles', 'age': 1,  'active': false }
     * ];
     *
     * _.partition(users, function(o) { return o.active; });
     * // => objects for [['fred'], ['barney', 'pebbles']]
     *
     * // The `_.matches` iteratee shorthand.
     * _.partition(users, { 'age': 1, 'active': false });
     * // => objects for [['pebbles'], ['barney', 'fred']]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.partition(users, ['active', false]);
     * // => objects for [['barney', 'pebbles'], ['fred']]
     *
     * // The `_.property` iteratee shorthand.
     * _.partition(users, 'active');
     * // => objects for [['fred'], ['barney', 'pebbles']]
     */
    var partition = createAggregator(function(result, value, key) {
      result[key ? 0 : 1].push(value);
    }, function() { return [[], []]; });

    /**
     * Reduces `collection` to a value which is the accumulated result of running
     * each element in `collection` thru `iteratee`, where each successive
     * invocation is supplied the return value of the previous. If `accumulator`
     * is not given, the first element of `collection` is used as the initial
     * value. The iteratee is invoked with four arguments:
     * (accumulator, value, index|key, collection).
     *
     * Many lodash methods are guarded to work as iteratees for methods like
     * `_.reduce`, `_.reduceRight`, and `_.transform`.
     *
     * The guarded methods are:
     * `assign`, `defaults`, `defaultsDeep`, `includes`, `merge`, `orderBy`,
     * and `sortBy`
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduceRight
     * @example
     *
     * _.reduce([1, 2], function(sum, n) {
     *   return sum + n;
     * }, 0);
     * // => 3
     *
     * _.reduce({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     *   return result;
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] } (iteration order is not guaranteed)
     */
    function reduce(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduce : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEach);
    }

    /**
     * This method is like `_.reduce` except that it iterates over elements of
     * `collection` from right to left.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The initial value.
     * @returns {*} Returns the accumulated value.
     * @see _.reduce
     * @example
     *
     * var array = [[0, 1], [2, 3], [4, 5]];
     *
     * _.reduceRight(array, function(flattened, other) {
     *   return flattened.concat(other);
     * }, []);
     * // => [4, 5, 2, 3, 0, 1]
     */
    function reduceRight(collection, iteratee, accumulator) {
      var func = isArray(collection) ? arrayReduceRight : baseReduce,
          initAccum = arguments.length < 3;

      return func(collection, getIteratee(iteratee, 4), accumulator, initAccum, baseEachRight);
    }

    /**
     * The opposite of `_.filter`; this method returns the elements of `collection`
     * that `predicate` does **not** return truthy for.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the new filtered array.
     * @see _.filter
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': false },
     *   { 'user': 'fred',   'age': 40, 'active': true }
     * ];
     *
     * _.reject(users, function(o) { return !o.active; });
     * // => objects for ['fred']
     *
     * // The `_.matches` iteratee shorthand.
     * _.reject(users, { 'age': 40, 'active': true });
     * // => objects for ['barney']
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.reject(users, ['active', false]);
     * // => objects for ['fred']
     *
     * // The `_.property` iteratee shorthand.
     * _.reject(users, 'active');
     * // => objects for ['barney']
     */
    function reject(collection, predicate) {
      var func = isArray(collection) ? arrayFilter : baseFilter;
      return func(collection, negate(getIteratee(predicate, 3)));
    }

    /**
     * Gets a random element from `collection`.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @returns {*} Returns the random element.
     * @example
     *
     * _.sample([1, 2, 3, 4]);
     * // => 2
     */
    function sample(collection) {
      var func = isArray(collection) ? arraySample : baseSample;
      return func(collection);
    }

    /**
     * Gets `n` random elements at unique keys from `collection` up to the
     * size of `collection`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Collection
     * @param {Array|Object} collection The collection to sample.
     * @param {number} [n=1] The number of elements to sample.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the random elements.
     * @example
     *
     * _.sampleSize([1, 2, 3], 2);
     * // => [3, 1]
     *
     * _.sampleSize([1, 2, 3], 4);
     * // => [2, 3, 1]
     */
    function sampleSize(collection, n, guard) {
      if ((guard ? isIterateeCall(collection, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = toInteger(n);
      }
      var func = isArray(collection) ? arraySampleSize : baseSampleSize;
      return func(collection, n);
    }

    /**
     * Creates an array of shuffled values, using a version of the
     * [Fisher-Yates shuffle](https://en.wikipedia.org/wiki/Fisher-Yates_shuffle).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to shuffle.
     * @returns {Array} Returns the new shuffled array.
     * @example
     *
     * _.shuffle([1, 2, 3, 4]);
     * // => [4, 1, 3, 2]
     */
    function shuffle(collection) {
      var func = isArray(collection) ? arrayShuffle : baseShuffle;
      return func(collection);
    }

    /**
     * Gets the size of `collection` by returning its length for array-like
     * values or the number of own enumerable string keyed properties for objects.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object|string} collection The collection to inspect.
     * @returns {number} Returns the collection size.
     * @example
     *
     * _.size([1, 2, 3]);
     * // => 3
     *
     * _.size({ 'a': 1, 'b': 2 });
     * // => 2
     *
     * _.size('pebbles');
     * // => 7
     */
    function size(collection) {
      if (collection == null) {
        return 0;
      }
      if (isArrayLike(collection)) {
        return isString(collection) ? stringSize(collection) : collection.length;
      }
      var tag = getTag(collection);
      if (tag == mapTag || tag == setTag) {
        return collection.size;
      }
      return baseKeys(collection).length;
    }

    /**
     * Checks if `predicate` returns truthy for **any** element of `collection`.
     * Iteration is stopped once `predicate` returns truthy. The predicate is
     * invoked with three arguments: (value, index|key, collection).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {boolean} Returns `true` if any element passes the predicate check,
     *  else `false`.
     * @example
     *
     * _.some([null, 0, 'yes', false], Boolean);
     * // => true
     *
     * var users = [
     *   { 'user': 'barney', 'active': true },
     *   { 'user': 'fred',   'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.some(users, { 'user': 'barney', 'active': false });
     * // => false
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.some(users, ['active', false]);
     * // => true
     *
     * // The `_.property` iteratee shorthand.
     * _.some(users, 'active');
     * // => true
     */
    function some(collection, predicate, guard) {
      var func = isArray(collection) ? arraySome : baseSome;
      if (guard && isIterateeCall(collection, predicate, guard)) {
        predicate = undefined;
      }
      return func(collection, getIteratee(predicate, 3));
    }

    /**
     * Creates an array of elements, sorted in ascending order by the results of
     * running each element in a collection thru each iteratee. This method
     * performs a stable sort, that is, it preserves the original sort order of
     * equal elements. The iteratees are invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Collection
     * @param {Array|Object} collection The collection to iterate over.
     * @param {...(Function|Function[])} [iteratees=[_.identity]]
     *  The iteratees to sort by.
     * @returns {Array} Returns the new sorted array.
     * @example
     *
     * var users = [
     *   { 'user': 'fred',   'age': 48 },
     *   { 'user': 'barney', 'age': 36 },
     *   { 'user': 'fred',   'age': 30 },
     *   { 'user': 'barney', 'age': 34 }
     * ];
     *
     * _.sortBy(users, [function(o) { return o.user; }]);
     * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
     *
     * _.sortBy(users, ['user', 'age']);
     * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
     */
    var sortBy = baseRest(function(collection, iteratees) {
      if (collection == null) {
        return [];
      }
      var length = iteratees.length;
      if (length > 1 && isIterateeCall(collection, iteratees[0], iteratees[1])) {
        iteratees = [];
      } else if (length > 2 && isIterateeCall(iteratees[0], iteratees[1], iteratees[2])) {
        iteratees = [iteratees[0]];
      }
      return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
    });

    /*------------------------------------------------------------------------*/

    /**
     * Gets the timestamp of the number of milliseconds that have elapsed since
     * the Unix epoch (1 January 1970 00:00:00 UTC).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Date
     * @returns {number} Returns the timestamp.
     * @example
     *
     * _.defer(function(stamp) {
     *   console.log(_.now() - stamp);
     * }, _.now());
     * // => Logs the number of milliseconds it took for the deferred invocation.
     */
    var now = ctxNow || function() {
      return root.Date.now();
    };

    /*------------------------------------------------------------------------*/

    /**
     * The opposite of `_.before`; this method creates a function that invokes
     * `func` once it's called `n` or more times.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {number} n The number of calls before `func` is invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var saves = ['profile', 'settings'];
     *
     * var done = _.after(saves.length, function() {
     *   console.log('done saving!');
     * });
     *
     * _.forEach(saves, function(type) {
     *   asyncSave({ 'type': type, 'complete': done });
     * });
     * // => Logs 'done saving!' after the two async saves have completed.
     */
    function after(n, func) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n < 1) {
          return func.apply(this, arguments);
        }
      };
    }

    /**
     * Creates a function that invokes `func`, with up to `n` arguments,
     * ignoring any additional arguments.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @param {number} [n=func.length] The arity cap.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.ary(parseInt, 1));
     * // => [6, 8, 10]
     */
    function ary(func, n, guard) {
      n = guard ? undefined : n;
      n = (func && n == null) ? func.length : n;
      return createWrap(func, WRAP_ARY_FLAG, undefined, undefined, undefined, undefined, n);
    }

    /**
     * Creates a function that invokes `func`, with the `this` binding and arguments
     * of the created function, while it's called less than `n` times. Subsequent
     * calls to the created function return the result of the last `func` invocation.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {number} n The number of calls at which `func` is no longer invoked.
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * jQuery(element).on('click', _.before(5, addContactToList));
     * // => Allows adding up to 4 contacts to the list.
     */
    function before(n, func) {
      var result;
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      n = toInteger(n);
      return function() {
        if (--n > 0) {
          result = func.apply(this, arguments);
        }
        if (n <= 1) {
          func = undefined;
        }
        return result;
      };
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of `thisArg`
     * and `partials` prepended to the arguments it receives.
     *
     * The `_.bind.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for partially applied arguments.
     *
     * **Note:** Unlike native `Function#bind`, this method doesn't set the "length"
     * property of bound functions.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to bind.
     * @param {*} thisArg The `this` binding of `func`.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * function greet(greeting, punctuation) {
     *   return greeting + ' ' + this.user + punctuation;
     * }
     *
     * var object = { 'user': 'fred' };
     *
     * var bound = _.bind(greet, object, 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bind(greet, object, _, '!');
     * bound('hi');
     * // => 'hi fred!'
     */
    var bind = baseRest(function(func, thisArg, partials) {
      var bitmask = WRAP_BIND_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bind));
        bitmask |= WRAP_PARTIAL_FLAG;
      }
      return createWrap(func, bitmask, thisArg, partials, holders);
    });

    /**
     * Creates a function that invokes the method at `object[key]` with `partials`
     * prepended to the arguments it receives.
     *
     * This method differs from `_.bind` by allowing bound functions to reference
     * methods that may be redefined or don't yet exist. See
     * [Peter Michaux's article](http://peter.michaux.ca/articles/lazy-function-definition-pattern)
     * for more details.
     *
     * The `_.bindKey.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Function
     * @param {Object} object The object to invoke the method on.
     * @param {string} key The key of the method.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new bound function.
     * @example
     *
     * var object = {
     *   'user': 'fred',
     *   'greet': function(greeting, punctuation) {
     *     return greeting + ' ' + this.user + punctuation;
     *   }
     * };
     *
     * var bound = _.bindKey(object, 'greet', 'hi');
     * bound('!');
     * // => 'hi fred!'
     *
     * object.greet = function(greeting, punctuation) {
     *   return greeting + 'ya ' + this.user + punctuation;
     * };
     *
     * bound('!');
     * // => 'hiya fred!'
     *
     * // Bound with placeholders.
     * var bound = _.bindKey(object, 'greet', _, '!');
     * bound('hi');
     * // => 'hiya fred!'
     */
    var bindKey = baseRest(function(object, key, partials) {
      var bitmask = WRAP_BIND_FLAG | WRAP_BIND_KEY_FLAG;
      if (partials.length) {
        var holders = replaceHolders(partials, getHolder(bindKey));
        bitmask |= WRAP_PARTIAL_FLAG;
      }
      return createWrap(key, bitmask, object, partials, holders);
    });

    /**
     * Creates a function that accepts arguments of `func` and either invokes
     * `func` returning its result, if at least `arity` number of arguments have
     * been provided, or returns a function that accepts the remaining `func`
     * arguments, and so on. The arity of `func` may be specified if `func.length`
     * is not sufficient.
     *
     * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
     * may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curry(abc);
     *
     * curried(1)(2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2)(3);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(1)(_, 3)(2);
     * // => [1, 2, 3]
     */
    function curry(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrap(func, WRAP_CURRY_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curry.placeholder;
      return result;
    }

    /**
     * This method is like `_.curry` except that arguments are applied to `func`
     * in the manner of `_.partialRight` instead of `_.partial`.
     *
     * The `_.curryRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for provided arguments.
     *
     * **Note:** This method doesn't set the "length" property of curried functions.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to curry.
     * @param {number} [arity=func.length] The arity of `func`.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the new curried function.
     * @example
     *
     * var abc = function(a, b, c) {
     *   return [a, b, c];
     * };
     *
     * var curried = _.curryRight(abc);
     *
     * curried(3)(2)(1);
     * // => [1, 2, 3]
     *
     * curried(2, 3)(1);
     * // => [1, 2, 3]
     *
     * curried(1, 2, 3);
     * // => [1, 2, 3]
     *
     * // Curried with placeholders.
     * curried(3)(1, _)(2);
     * // => [1, 2, 3]
     */
    function curryRight(func, arity, guard) {
      arity = guard ? undefined : arity;
      var result = createWrap(func, WRAP_CURRY_RIGHT_FLAG, undefined, undefined, undefined, undefined, undefined, arity);
      result.placeholder = curryRight.placeholder;
      return result;
    }

    /**
     * Creates a debounced function that delays invoking `func` until after `wait`
     * milliseconds have elapsed since the last time the debounced function was
     * invoked. The debounced function comes with a `cancel` method to cancel
     * delayed `func` invocations and a `flush` method to immediately invoke them.
     * Provide `options` to indicate whether `func` should be invoked on the
     * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
     * with the last arguments provided to the debounced function. Subsequent
     * calls to the debounced function return the result of the last `func`
     * invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the debounced function
     * is invoked more than once during the `wait` timeout.
     *
     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.debounce` and `_.throttle`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to debounce.
     * @param {number} [wait=0] The number of milliseconds to delay.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=false]
     *  Specify invoking on the leading edge of the timeout.
     * @param {number} [options.maxWait]
     *  The maximum time `func` is allowed to be delayed before it's invoked.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new debounced function.
     * @example
     *
     * // Avoid costly calculations while the window size is in flux.
     * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
     *
     * // Invoke `sendMail` when clicked, debouncing subsequent calls.
     * jQuery(element).on('click', _.debounce(sendMail, 300, {
     *   'leading': true,
     *   'trailing': false
     * }));
     *
     * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
     * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
     * var source = new EventSource('/stream');
     * jQuery(source).on('message', debounced);
     *
     * // Cancel the trailing debounced invocation.
     * jQuery(window).on('popstate', debounced.cancel);
     */
    function debounce(func, wait, options) {
      var lastArgs,
          lastThis,
          maxWait,
          result,
          timerId,
          lastCallTime,
          lastInvokeTime = 0,
          leading = false,
          maxing = false,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      wait = toNumber(wait) || 0;
      if (isObject(options)) {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }

      function invokeFunc(time) {
        var args = lastArgs,
            thisArg = lastThis;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result;
      }

      function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time;
        // Start the timer for the trailing edge.
        timerId = setTimeout(timerExpired, wait);
        // Invoke the leading edge.
        return leading ? invokeFunc(time) : result;
      }

      function remainingWait(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime,
            timeWaiting = wait - timeSinceLastCall;

        return maxing
          ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
          : timeWaiting;
      }

      function shouldInvoke(time) {
        var timeSinceLastCall = time - lastCallTime,
            timeSinceLastInvoke = time - lastInvokeTime;

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
          (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
      }

      function timerExpired() {
        var time = now();
        if (shouldInvoke(time)) {
          return trailingEdge(time);
        }
        // Restart the timer.
        timerId = setTimeout(timerExpired, remainingWait(time));
      }

      function trailingEdge(time) {
        timerId = undefined;

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArgs) {
          return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
      }

      function cancel() {
        if (timerId !== undefined) {
          clearTimeout(timerId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timerId = undefined;
      }

      function flush() {
        return timerId === undefined ? result : trailingEdge(now());
      }

      function debounced() {
        var time = now(),
            isInvoking = shouldInvoke(time);

        lastArgs = arguments;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
          if (timerId === undefined) {
            return leadingEdge(lastCallTime);
          }
          if (maxing) {
            // Handle invocations in a tight loop.
            clearTimeout(timerId);
            timerId = setTimeout(timerExpired, wait);
            return invokeFunc(lastCallTime);
          }
        }
        if (timerId === undefined) {
          timerId = setTimeout(timerExpired, wait);
        }
        return result;
      }
      debounced.cancel = cancel;
      debounced.flush = flush;
      return debounced;
    }

    /**
     * Defers invoking the `func` until the current call stack has cleared. Any
     * additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to defer.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.defer(function(text) {
     *   console.log(text);
     * }, 'deferred');
     * // => Logs 'deferred' after one millisecond.
     */
    var defer = baseRest(function(func, args) {
      return baseDelay(func, 1, args);
    });

    /**
     * Invokes `func` after `wait` milliseconds. Any additional arguments are
     * provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to delay.
     * @param {number} wait The number of milliseconds to delay invocation.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {number} Returns the timer id.
     * @example
     *
     * _.delay(function(text) {
     *   console.log(text);
     * }, 1000, 'later');
     * // => Logs 'later' after one second.
     */
    var delay = baseRest(function(func, wait, args) {
      return baseDelay(func, toNumber(wait) || 0, args);
    });

    /**
     * Creates a function that invokes `func` with arguments reversed.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to flip arguments for.
     * @returns {Function} Returns the new flipped function.
     * @example
     *
     * var flipped = _.flip(function() {
     *   return _.toArray(arguments);
     * });
     *
     * flipped('a', 'b', 'c', 'd');
     * // => ['d', 'c', 'b', 'a']
     */
    function flip(func) {
      return createWrap(func, WRAP_FLIP_FLAG);
    }

    /**
     * Creates a function that memoizes the result of `func`. If `resolver` is
     * provided, it determines the cache key for storing the result based on the
     * arguments provided to the memoized function. By default, the first argument
     * provided to the memoized function is used as the map cache key. The `func`
     * is invoked with the `this` binding of the memoized function.
     *
     * **Note:** The cache is exposed as the `cache` property on the memoized
     * function. Its creation may be customized by replacing the `_.memoize.Cache`
     * constructor with one whose instances implement the
     * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
     * method interface of `clear`, `delete`, `get`, `has`, and `set`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to have its output memoized.
     * @param {Function} [resolver] The function to resolve the cache key.
     * @returns {Function} Returns the new memoized function.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     * var other = { 'c': 3, 'd': 4 };
     *
     * var values = _.memoize(_.values);
     * values(object);
     * // => [1, 2]
     *
     * values(other);
     * // => [3, 4]
     *
     * object.a = 2;
     * values(object);
     * // => [1, 2]
     *
     * // Modify the result cache.
     * values.cache.set(object, ['a', 'b']);
     * values(object);
     * // => ['a', 'b']
     *
     * // Replace `_.memoize.Cache`.
     * _.memoize.Cache = WeakMap;
     */
    function memoize(func, resolver) {
      if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      var memoized = function() {
        var args = arguments,
            key = resolver ? resolver.apply(this, args) : args[0],
            cache = memoized.cache;

        if (cache.has(key)) {
          return cache.get(key);
        }
        var result = func.apply(this, args);
        memoized.cache = cache.set(key, result) || cache;
        return result;
      };
      memoized.cache = new (memoize.Cache || MapCache);
      return memoized;
    }

    // Expose `MapCache`.
    memoize.Cache = MapCache;

    /**
     * Creates a function that negates the result of the predicate `func`. The
     * `func` predicate is invoked with the `this` binding and arguments of the
     * created function.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} predicate The predicate to negate.
     * @returns {Function} Returns the new negated function.
     * @example
     *
     * function isEven(n) {
     *   return n % 2 == 0;
     * }
     *
     * _.filter([1, 2, 3, 4, 5, 6], _.negate(isEven));
     * // => [1, 3, 5]
     */
    function negate(predicate) {
      if (typeof predicate != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      return function() {
        var args = arguments;
        switch (args.length) {
          case 0: return !predicate.call(this);
          case 1: return !predicate.call(this, args[0]);
          case 2: return !predicate.call(this, args[0], args[1]);
          case 3: return !predicate.call(this, args[0], args[1], args[2]);
        }
        return !predicate.apply(this, args);
      };
    }

    /**
     * Creates a function that is restricted to invoking `func` once. Repeat calls
     * to the function return the value of the first invocation. The `func` is
     * invoked with the `this` binding and arguments of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to restrict.
     * @returns {Function} Returns the new restricted function.
     * @example
     *
     * var initialize = _.once(createApplication);
     * initialize();
     * initialize();
     * // => `createApplication` is invoked once
     */
    function once(func) {
      return before(2, func);
    }

    /**
     * Creates a function that invokes `func` with its arguments transformed.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Function
     * @param {Function} func The function to wrap.
     * @param {...(Function|Function[])} [transforms=[_.identity]]
     *  The argument transforms.
     * @returns {Function} Returns the new function.
     * @example
     *
     * function doubled(n) {
     *   return n * 2;
     * }
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var func = _.overArgs(function(x, y) {
     *   return [x, y];
     * }, [square, doubled]);
     *
     * func(9, 3);
     * // => [81, 6]
     *
     * func(10, 5);
     * // => [100, 10]
     */
    var overArgs = castRest(function(func, transforms) {
      transforms = (transforms.length == 1 && isArray(transforms[0]))
        ? arrayMap(transforms[0], baseUnary(getIteratee()))
        : arrayMap(baseFlatten(transforms, 1), baseUnary(getIteratee()));

      var funcsLength = transforms.length;
      return baseRest(function(args) {
        var index = -1,
            length = nativeMin(args.length, funcsLength);

        while (++index < length) {
          args[index] = transforms[index].call(this, args[index]);
        }
        return apply(func, this, args);
      });
    });

    /**
     * Creates a function that invokes `func` with `partials` prepended to the
     * arguments it receives. This method is like `_.bind` except it does **not**
     * alter the `this` binding.
     *
     * The `_.partial.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 0.2.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * function greet(greeting, name) {
     *   return greeting + ' ' + name;
     * }
     *
     * var sayHelloTo = _.partial(greet, 'hello');
     * sayHelloTo('fred');
     * // => 'hello fred'
     *
     * // Partially applied with placeholders.
     * var greetFred = _.partial(greet, _, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     */
    var partial = baseRest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partial));
      return createWrap(func, WRAP_PARTIAL_FLAG, undefined, partials, holders);
    });

    /**
     * This method is like `_.partial` except that partially applied arguments
     * are appended to the arguments it receives.
     *
     * The `_.partialRight.placeholder` value, which defaults to `_` in monolithic
     * builds, may be used as a placeholder for partially applied arguments.
     *
     * **Note:** This method doesn't set the "length" property of partially
     * applied functions.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Function
     * @param {Function} func The function to partially apply arguments to.
     * @param {...*} [partials] The arguments to be partially applied.
     * @returns {Function} Returns the new partially applied function.
     * @example
     *
     * function greet(greeting, name) {
     *   return greeting + ' ' + name;
     * }
     *
     * var greetFred = _.partialRight(greet, 'fred');
     * greetFred('hi');
     * // => 'hi fred'
     *
     * // Partially applied with placeholders.
     * var sayHelloTo = _.partialRight(greet, 'hello', _);
     * sayHelloTo('fred');
     * // => 'hello fred'
     */
    var partialRight = baseRest(function(func, partials) {
      var holders = replaceHolders(partials, getHolder(partialRight));
      return createWrap(func, WRAP_PARTIAL_RIGHT_FLAG, undefined, partials, holders);
    });

    /**
     * Creates a function that invokes `func` with arguments arranged according
     * to the specified `indexes` where the argument value at the first index is
     * provided as the first argument, the argument value at the second index is
     * provided as the second argument, and so on.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Function
     * @param {Function} func The function to rearrange arguments for.
     * @param {...(number|number[])} indexes The arranged argument indexes.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var rearged = _.rearg(function(a, b, c) {
     *   return [a, b, c];
     * }, [2, 0, 1]);
     *
     * rearged('b', 'c', 'a')
     * // => ['a', 'b', 'c']
     */
    var rearg = flatRest(function(func, indexes) {
      return createWrap(func, WRAP_REARG_FLAG, undefined, undefined, undefined, indexes);
    });

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * created function and arguments from `start` and beyond provided as
     * an array.
     *
     * **Note:** This method is based on the
     * [rest parameter](https://mdn.io/rest_parameters).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to apply a rest parameter to.
     * @param {number} [start=func.length-1] The start position of the rest parameter.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.rest(function(what, names) {
     *   return what + ' ' + _.initial(names).join(', ') +
     *     (_.size(names) > 1 ? ', & ' : '') + _.last(names);
     * });
     *
     * say('hello', 'fred', 'barney', 'pebbles');
     * // => 'hello fred, barney, & pebbles'
     */
    function rest(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = start === undefined ? start : toInteger(start);
      return baseRest(func, start);
    }

    /**
     * Creates a function that invokes `func` with the `this` binding of the
     * create function and an array of arguments much like
     * [`Function#apply`](http://www.ecma-international.org/ecma-262/7.0/#sec-function.prototype.apply).
     *
     * **Note:** This method is based on the
     * [spread operator](https://mdn.io/spread_operator).
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Function
     * @param {Function} func The function to spread arguments over.
     * @param {number} [start=0] The start position of the spread.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var say = _.spread(function(who, what) {
     *   return who + ' says ' + what;
     * });
     *
     * say(['fred', 'hello']);
     * // => 'fred says hello'
     *
     * var numbers = Promise.all([
     *   Promise.resolve(40),
     *   Promise.resolve(36)
     * ]);
     *
     * numbers.then(_.spread(function(x, y) {
     *   return x + y;
     * }));
     * // => a Promise of 76
     */
    function spread(func, start) {
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      start = start == null ? 0 : nativeMax(toInteger(start), 0);
      return baseRest(function(args) {
        var array = args[start],
            otherArgs = castSlice(args, 0, start);

        if (array) {
          arrayPush(otherArgs, array);
        }
        return apply(func, this, otherArgs);
      });
    }

    /**
     * Creates a throttled function that only invokes `func` at most once per
     * every `wait` milliseconds. The throttled function comes with a `cancel`
     * method to cancel delayed `func` invocations and a `flush` method to
     * immediately invoke them. Provide `options` to indicate whether `func`
     * should be invoked on the leading and/or trailing edge of the `wait`
     * timeout. The `func` is invoked with the last arguments provided to the
     * throttled function. Subsequent calls to the throttled function return the
     * result of the last `func` invocation.
     *
     * **Note:** If `leading` and `trailing` options are `true`, `func` is
     * invoked on the trailing edge of the timeout only if the throttled function
     * is invoked more than once during the `wait` timeout.
     *
     * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
     * until to the next tick, similar to `setTimeout` with a timeout of `0`.
     *
     * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
     * for details over the differences between `_.throttle` and `_.debounce`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {Function} func The function to throttle.
     * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.leading=true]
     *  Specify invoking on the leading edge of the timeout.
     * @param {boolean} [options.trailing=true]
     *  Specify invoking on the trailing edge of the timeout.
     * @returns {Function} Returns the new throttled function.
     * @example
     *
     * // Avoid excessively updating the position while scrolling.
     * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
     *
     * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
     * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
     * jQuery(element).on('click', throttled);
     *
     * // Cancel the trailing throttled invocation.
     * jQuery(window).on('popstate', throttled.cancel);
     */
    function throttle(func, wait, options) {
      var leading = true,
          trailing = true;

      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      if (isObject(options)) {
        leading = 'leading' in options ? !!options.leading : leading;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
      }
      return debounce(func, wait, {
        'leading': leading,
        'maxWait': wait,
        'trailing': trailing
      });
    }

    /**
     * Creates a function that accepts up to one argument, ignoring any
     * additional arguments.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Function
     * @param {Function} func The function to cap arguments for.
     * @returns {Function} Returns the new capped function.
     * @example
     *
     * _.map(['6', '8', '10'], _.unary(parseInt));
     * // => [6, 8, 10]
     */
    function unary(func) {
      return ary(func, 1);
    }

    /**
     * Creates a function that provides `value` to `wrapper` as its first
     * argument. Any additional arguments provided to the function are appended
     * to those provided to the `wrapper`. The wrapper is invoked with the `this`
     * binding of the created function.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Function
     * @param {*} value The value to wrap.
     * @param {Function} [wrapper=identity] The wrapper function.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var p = _.wrap(_.escape, function(func, text) {
     *   return '<p>' + func(text) + '</p>';
     * });
     *
     * p('fred, barney, & pebbles');
     * // => '<p>fred, barney, &amp; pebbles</p>'
     */
    function wrap(value, wrapper) {
      return partial(castFunction(wrapper), value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Casts `value` as an array if it's not one.
     *
     * @static
     * @memberOf _
     * @since 4.4.0
     * @category Lang
     * @param {*} value The value to inspect.
     * @returns {Array} Returns the cast array.
     * @example
     *
     * _.castArray(1);
     * // => [1]
     *
     * _.castArray({ 'a': 1 });
     * // => [{ 'a': 1 }]
     *
     * _.castArray('abc');
     * // => ['abc']
     *
     * _.castArray(null);
     * // => [null]
     *
     * _.castArray(undefined);
     * // => [undefined]
     *
     * _.castArray();
     * // => []
     *
     * var array = [1, 2, 3];
     * console.log(_.castArray(array) === array);
     * // => true
     */
    function castArray() {
      if (!arguments.length) {
        return [];
      }
      var value = arguments[0];
      return isArray(value) ? value : [value];
    }

    /**
     * Creates a shallow clone of `value`.
     *
     * **Note:** This method is loosely based on the
     * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
     * and supports cloning arrays, array buffers, booleans, date objects, maps,
     * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
     * arrays. The own enumerable properties of `arguments` objects are cloned
     * as plain objects. An empty object is returned for uncloneable values such
     * as error objects, functions, DOM nodes, and WeakMaps.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to clone.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeep
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var shallow = _.clone(objects);
     * console.log(shallow[0] === objects[0]);
     * // => true
     */
    function clone(value) {
      return baseClone(value, CLONE_SYMBOLS_FLAG);
    }

    /**
     * This method is like `_.clone` except that it accepts `customizer` which
     * is invoked to produce the cloned value. If `customizer` returns `undefined`,
     * cloning is handled by the method instead. The `customizer` is invoked with
     * up to four arguments; (value [, index|key, object, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the cloned value.
     * @see _.cloneDeepWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(false);
     *   }
     * }
     *
     * var el = _.cloneWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 0
     */
    function cloneWith(value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseClone(value, CLONE_SYMBOLS_FLAG, customizer);
    }

    /**
     * This method is like `_.clone` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @returns {*} Returns the deep cloned value.
     * @see _.clone
     * @example
     *
     * var objects = [{ 'a': 1 }, { 'b': 2 }];
     *
     * var deep = _.cloneDeep(objects);
     * console.log(deep[0] === objects[0]);
     * // => false
     */
    function cloneDeep(value) {
      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
    }

    /**
     * This method is like `_.cloneWith` except that it recursively clones `value`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to recursively clone.
     * @param {Function} [customizer] The function to customize cloning.
     * @returns {*} Returns the deep cloned value.
     * @see _.cloneWith
     * @example
     *
     * function customizer(value) {
     *   if (_.isElement(value)) {
     *     return value.cloneNode(true);
     *   }
     * }
     *
     * var el = _.cloneDeepWith(document.body, customizer);
     *
     * console.log(el === document.body);
     * // => false
     * console.log(el.nodeName);
     * // => 'BODY'
     * console.log(el.childNodes.length);
     * // => 20
     */
    function cloneDeepWith(value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG, customizer);
    }

    /**
     * Checks if `object` conforms to `source` by invoking the predicate
     * properties of `source` with the corresponding property values of `object`.
     *
     * **Note:** This method is equivalent to `_.conforms` when `source` is
     * partially applied.
     *
     * @static
     * @memberOf _
     * @since 4.14.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property predicates to conform to.
     * @returns {boolean} Returns `true` if `object` conforms, else `false`.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     *
     * _.conformsTo(object, { 'b': function(n) { return n > 1; } });
     * // => true
     *
     * _.conformsTo(object, { 'b': function(n) { return n > 2; } });
     * // => false
     */
    function conformsTo(object, source) {
      return source == null || baseConformsTo(object, source, keys(source));
    }

    /**
     * Performs a
     * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
     * comparison between two values to determine if they are equivalent.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.eq(object, object);
     * // => true
     *
     * _.eq(object, other);
     * // => false
     *
     * _.eq('a', 'a');
     * // => true
     *
     * _.eq('a', Object('a'));
     * // => false
     *
     * _.eq(NaN, NaN);
     * // => true
     */
    function eq(value, other) {
      return value === other || (value !== value && other !== other);
    }

    /**
     * Checks if `value` is greater than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than `other`,
     *  else `false`.
     * @see _.lt
     * @example
     *
     * _.gt(3, 1);
     * // => true
     *
     * _.gt(3, 3);
     * // => false
     *
     * _.gt(1, 3);
     * // => false
     */
    var gt = createRelationalOperation(baseGt);

    /**
     * Checks if `value` is greater than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is greater than or equal to
     *  `other`, else `false`.
     * @see _.lte
     * @example
     *
     * _.gte(3, 1);
     * // => true
     *
     * _.gte(3, 3);
     * // => true
     *
     * _.gte(1, 3);
     * // => false
     */
    var gte = createRelationalOperation(function(value, other) {
      return value >= other;
    });

    /**
     * Checks if `value` is likely an `arguments` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an `arguments` object,
     *  else `false`.
     * @example
     *
     * _.isArguments(function() { return arguments; }());
     * // => true
     *
     * _.isArguments([1, 2, 3]);
     * // => false
     */
    var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
      return isObjectLike(value) && hasOwnProperty.call(value, 'callee') &&
        !propertyIsEnumerable.call(value, 'callee');
    };

    /**
     * Checks if `value` is classified as an `Array` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array, else `false`.
     * @example
     *
     * _.isArray([1, 2, 3]);
     * // => true
     *
     * _.isArray(document.body.children);
     * // => false
     *
     * _.isArray('abc');
     * // => false
     *
     * _.isArray(_.noop);
     * // => false
     */
    var isArray = Array.isArray;

    /**
     * Checks if `value` is classified as an `ArrayBuffer` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array buffer, else `false`.
     * @example
     *
     * _.isArrayBuffer(new ArrayBuffer(2));
     * // => true
     *
     * _.isArrayBuffer(new Array(2));
     * // => false
     */
    var isArrayBuffer = nodeIsArrayBuffer ? baseUnary(nodeIsArrayBuffer) : baseIsArrayBuffer;

    /**
     * Checks if `value` is array-like. A value is considered array-like if it's
     * not a function and has a `value.length` that's an integer greater than or
     * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
     * @example
     *
     * _.isArrayLike([1, 2, 3]);
     * // => true
     *
     * _.isArrayLike(document.body.children);
     * // => true
     *
     * _.isArrayLike('abc');
     * // => true
     *
     * _.isArrayLike(_.noop);
     * // => false
     */
    function isArrayLike(value) {
      return value != null && isLength(value.length) && !isFunction(value);
    }

    /**
     * This method is like `_.isArrayLike` except that it also checks if `value`
     * is an object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an array-like object,
     *  else `false`.
     * @example
     *
     * _.isArrayLikeObject([1, 2, 3]);
     * // => true
     *
     * _.isArrayLikeObject(document.body.children);
     * // => true
     *
     * _.isArrayLikeObject('abc');
     * // => false
     *
     * _.isArrayLikeObject(_.noop);
     * // => false
     */
    function isArrayLikeObject(value) {
      return isObjectLike(value) && isArrayLike(value);
    }

    /**
     * Checks if `value` is classified as a boolean primitive or object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a boolean, else `false`.
     * @example
     *
     * _.isBoolean(false);
     * // => true
     *
     * _.isBoolean(null);
     * // => false
     */
    function isBoolean(value) {
      return value === true || value === false ||
        (isObjectLike(value) && baseGetTag(value) == boolTag);
    }

    /**
     * Checks if `value` is a buffer.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
     * @example
     *
     * _.isBuffer(new Buffer(2));
     * // => true
     *
     * _.isBuffer(new Uint8Array(2));
     * // => false
     */
    var isBuffer = nativeIsBuffer || stubFalse;

    /**
     * Checks if `value` is classified as a `Date` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a date object, else `false`.
     * @example
     *
     * _.isDate(new Date);
     * // => true
     *
     * _.isDate('Mon April 23 2012');
     * // => false
     */
    var isDate = nodeIsDate ? baseUnary(nodeIsDate) : baseIsDate;

    /**
     * Checks if `value` is likely a DOM element.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a DOM element, else `false`.
     * @example
     *
     * _.isElement(document.body);
     * // => true
     *
     * _.isElement('<body>');
     * // => false
     */
    function isElement(value) {
      return isObjectLike(value) && value.nodeType === 1 && !isPlainObject(value);
    }

    /**
     * Checks if `value` is an empty object, collection, map, or set.
     *
     * Objects are considered empty if they have no own enumerable string keyed
     * properties.
     *
     * Array-like values such as `arguments` objects, arrays, buffers, strings, or
     * jQuery-like collections are considered empty if they have a `length` of `0`.
     * Similarly, maps and sets are considered empty if they have a `size` of `0`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is empty, else `false`.
     * @example
     *
     * _.isEmpty(null);
     * // => true
     *
     * _.isEmpty(true);
     * // => true
     *
     * _.isEmpty(1);
     * // => true
     *
     * _.isEmpty([1, 2, 3]);
     * // => false
     *
     * _.isEmpty({ 'a': 1 });
     * // => false
     */
    function isEmpty(value) {
      if (value == null) {
        return true;
      }
      if (isArrayLike(value) &&
          (isArray(value) || typeof value == 'string' || typeof value.splice == 'function' ||
            isBuffer(value) || isTypedArray(value) || isArguments(value))) {
        return !value.length;
      }
      var tag = getTag(value);
      if (tag == mapTag || tag == setTag) {
        return !value.size;
      }
      if (isPrototype(value)) {
        return !baseKeys(value).length;
      }
      for (var key in value) {
        if (hasOwnProperty.call(value, key)) {
          return false;
        }
      }
      return true;
    }

    /**
     * Performs a deep comparison between two values to determine if they are
     * equivalent.
     *
     * **Note:** This method supports comparing arrays, array buffers, booleans,
     * date objects, error objects, maps, numbers, `Object` objects, regexes,
     * sets, strings, symbols, and typed arrays. `Object` objects are compared
     * by their own, not inherited, enumerable properties. Functions and DOM
     * nodes are compared by strict equality, i.e. `===`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * var object = { 'a': 1 };
     * var other = { 'a': 1 };
     *
     * _.isEqual(object, other);
     * // => true
     *
     * object === other;
     * // => false
     */
    function isEqual(value, other) {
      return baseIsEqual(value, other);
    }

    /**
     * This method is like `_.isEqual` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with up to
     * six arguments: (objValue, othValue [, index|key, object, other, stack]).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, othValue) {
     *   if (isGreeting(objValue) && isGreeting(othValue)) {
     *     return true;
     *   }
     * }
     *
     * var array = ['hello', 'goodbye'];
     * var other = ['hi', 'goodbye'];
     *
     * _.isEqualWith(array, other, customizer);
     * // => true
     */
    function isEqualWith(value, other, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      var result = customizer ? customizer(value, other) : undefined;
      return result === undefined ? baseIsEqual(value, other, undefined, customizer) : !!result;
    }

    /**
     * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
     * `SyntaxError`, `TypeError`, or `URIError` object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an error object, else `false`.
     * @example
     *
     * _.isError(new Error);
     * // => true
     *
     * _.isError(Error);
     * // => false
     */
    function isError(value) {
      if (!isObjectLike(value)) {
        return false;
      }
      var tag = baseGetTag(value);
      return tag == errorTag || tag == domExcTag ||
        (typeof value.message == 'string' && typeof value.name == 'string' && !isPlainObject(value));
    }

    /**
     * Checks if `value` is a finite primitive number.
     *
     * **Note:** This method is based on
     * [`Number.isFinite`](https://mdn.io/Number/isFinite).
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a finite number, else `false`.
     * @example
     *
     * _.isFinite(3);
     * // => true
     *
     * _.isFinite(Number.MIN_VALUE);
     * // => true
     *
     * _.isFinite(Infinity);
     * // => false
     *
     * _.isFinite('3');
     * // => false
     */
    function isFinite(value) {
      return typeof value == 'number' && nativeIsFinite(value);
    }

    /**
     * Checks if `value` is classified as a `Function` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a function, else `false`.
     * @example
     *
     * _.isFunction(_);
     * // => true
     *
     * _.isFunction(/abc/);
     * // => false
     */
    function isFunction(value) {
      if (!isObject(value)) {
        return false;
      }
      // The use of `Object#toString` avoids issues with the `typeof` operator
      // in Safari 9 which returns 'object' for typed arrays and other constructors.
      var tag = baseGetTag(value);
      return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
    }

    /**
     * Checks if `value` is an integer.
     *
     * **Note:** This method is based on
     * [`Number.isInteger`](https://mdn.io/Number/isInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
     * @example
     *
     * _.isInteger(3);
     * // => true
     *
     * _.isInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isInteger(Infinity);
     * // => false
     *
     * _.isInteger('3');
     * // => false
     */
    function isInteger(value) {
      return typeof value == 'number' && value == toInteger(value);
    }

    /**
     * Checks if `value` is a valid array-like length.
     *
     * **Note:** This method is loosely based on
     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
     * @example
     *
     * _.isLength(3);
     * // => true
     *
     * _.isLength(Number.MIN_VALUE);
     * // => false
     *
     * _.isLength(Infinity);
     * // => false
     *
     * _.isLength('3');
     * // => false
     */
    function isLength(value) {
      return typeof value == 'number' &&
        value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is the
     * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
     * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is an object, else `false`.
     * @example
     *
     * _.isObject({});
     * // => true
     *
     * _.isObject([1, 2, 3]);
     * // => true
     *
     * _.isObject(_.noop);
     * // => true
     *
     * _.isObject(null);
     * // => false
     */
    function isObject(value) {
      var type = typeof value;
      return value != null && (type == 'object' || type == 'function');
    }

    /**
     * Checks if `value` is object-like. A value is object-like if it's not `null`
     * and has a `typeof` result of "object".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
     * @example
     *
     * _.isObjectLike({});
     * // => true
     *
     * _.isObjectLike([1, 2, 3]);
     * // => true
     *
     * _.isObjectLike(_.noop);
     * // => false
     *
     * _.isObjectLike(null);
     * // => false
     */
    function isObjectLike(value) {
      return value != null && typeof value == 'object';
    }

    /**
     * Checks if `value` is classified as a `Map` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a map, else `false`.
     * @example
     *
     * _.isMap(new Map);
     * // => true
     *
     * _.isMap(new WeakMap);
     * // => false
     */
    var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

    /**
     * Performs a partial deep comparison between `object` and `source` to
     * determine if `object` contains equivalent property values.
     *
     * **Note:** This method is equivalent to `_.matches` when `source` is
     * partially applied.
     *
     * Partial comparisons will match empty array and empty object `source`
     * values against any array or object value, respectively. See `_.isEqual`
     * for a list of supported value comparisons.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * var object = { 'a': 1, 'b': 2 };
     *
     * _.isMatch(object, { 'b': 2 });
     * // => true
     *
     * _.isMatch(object, { 'b': 1 });
     * // => false
     */
    function isMatch(object, source) {
      return object === source || baseIsMatch(object, source, getMatchData(source));
    }

    /**
     * This method is like `_.isMatch` except that it accepts `customizer` which
     * is invoked to compare values. If `customizer` returns `undefined`, comparisons
     * are handled by the method instead. The `customizer` is invoked with five
     * arguments: (objValue, srcValue, index|key, object, source).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {Object} object The object to inspect.
     * @param {Object} source The object of property values to match.
     * @param {Function} [customizer] The function to customize comparisons.
     * @returns {boolean} Returns `true` if `object` is a match, else `false`.
     * @example
     *
     * function isGreeting(value) {
     *   return /^h(?:i|ello)$/.test(value);
     * }
     *
     * function customizer(objValue, srcValue) {
     *   if (isGreeting(objValue) && isGreeting(srcValue)) {
     *     return true;
     *   }
     * }
     *
     * var object = { 'greeting': 'hello' };
     * var source = { 'greeting': 'hi' };
     *
     * _.isMatchWith(object, source, customizer);
     * // => true
     */
    function isMatchWith(object, source, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return baseIsMatch(object, source, getMatchData(source), customizer);
    }

    /**
     * Checks if `value` is `NaN`.
     *
     * **Note:** This method is based on
     * [`Number.isNaN`](https://mdn.io/Number/isNaN) and is not the same as
     * global [`isNaN`](https://mdn.io/isNaN) which returns `true` for
     * `undefined` and other non-number values.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
     * @example
     *
     * _.isNaN(NaN);
     * // => true
     *
     * _.isNaN(new Number(NaN));
     * // => true
     *
     * isNaN(undefined);
     * // => true
     *
     * _.isNaN(undefined);
     * // => false
     */
    function isNaN(value) {
      // An `NaN` primitive is the only value that is not equal to itself.
      // Perform the `toStringTag` check first to avoid errors with some
      // ActiveX objects in IE.
      return isNumber(value) && value != +value;
    }

    /**
     * Checks if `value` is a pristine native function.
     *
     * **Note:** This method can't reliably detect native functions in the presence
     * of the core-js package because core-js circumvents this kind of detection.
     * Despite multiple requests, the core-js maintainer has made it clear: any
     * attempt to fix the detection will be obstructed. As a result, we're left
     * with little choice but to throw an error. Unfortunately, this also affects
     * packages, like [babel-polyfill](https://www.npmjs.com/package/babel-polyfill),
     * which rely on core-js.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a native function,
     *  else `false`.
     * @example
     *
     * _.isNative(Array.prototype.push);
     * // => true
     *
     * _.isNative(_);
     * // => false
     */
    function isNative(value) {
      if (isMaskable(value)) {
        throw new Error(CORE_ERROR_TEXT);
      }
      return baseIsNative(value);
    }

    /**
     * Checks if `value` is `null`.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `null`, else `false`.
     * @example
     *
     * _.isNull(null);
     * // => true
     *
     * _.isNull(void 0);
     * // => false
     */
    function isNull(value) {
      return value === null;
    }

    /**
     * Checks if `value` is `null` or `undefined`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
     * @example
     *
     * _.isNil(null);
     * // => true
     *
     * _.isNil(void 0);
     * // => true
     *
     * _.isNil(NaN);
     * // => false
     */
    function isNil(value) {
      return value == null;
    }

    /**
     * Checks if `value` is classified as a `Number` primitive or object.
     *
     * **Note:** To exclude `Infinity`, `-Infinity`, and `NaN`, which are
     * classified as numbers, use the `_.isFinite` method.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a number, else `false`.
     * @example
     *
     * _.isNumber(3);
     * // => true
     *
     * _.isNumber(Number.MIN_VALUE);
     * // => true
     *
     * _.isNumber(Infinity);
     * // => true
     *
     * _.isNumber('3');
     * // => false
     */
    function isNumber(value) {
      return typeof value == 'number' ||
        (isObjectLike(value) && baseGetTag(value) == numberTag);
    }

    /**
     * Checks if `value` is a plain object, that is, an object created by the
     * `Object` constructor or one with a `[[Prototype]]` of `null`.
     *
     * @static
     * @memberOf _
     * @since 0.8.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * _.isPlainObject(new Foo);
     * // => false
     *
     * _.isPlainObject([1, 2, 3]);
     * // => false
     *
     * _.isPlainObject({ 'x': 0, 'y': 0 });
     * // => true
     *
     * _.isPlainObject(Object.create(null));
     * // => true
     */
    function isPlainObject(value) {
      if (!isObjectLike(value) || baseGetTag(value) != objectTag) {
        return false;
      }
      var proto = getPrototype(value);
      if (proto === null) {
        return true;
      }
      var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
      return typeof Ctor == 'function' && Ctor instanceof Ctor &&
        funcToString.call(Ctor) == objectCtorString;
    }

    /**
     * Checks if `value` is classified as a `RegExp` object.
     *
     * @static
     * @memberOf _
     * @since 0.1.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a regexp, else `false`.
     * @example
     *
     * _.isRegExp(/abc/);
     * // => true
     *
     * _.isRegExp('/abc/');
     * // => false
     */
    var isRegExp = nodeIsRegExp ? baseUnary(nodeIsRegExp) : baseIsRegExp;

    /**
     * Checks if `value` is a safe integer. An integer is safe if it's an IEEE-754
     * double precision number which isn't the result of a rounded unsafe integer.
     *
     * **Note:** This method is based on
     * [`Number.isSafeInteger`](https://mdn.io/Number/isSafeInteger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a safe integer, else `false`.
     * @example
     *
     * _.isSafeInteger(3);
     * // => true
     *
     * _.isSafeInteger(Number.MIN_VALUE);
     * // => false
     *
     * _.isSafeInteger(Infinity);
     * // => false
     *
     * _.isSafeInteger('3');
     * // => false
     */
    function isSafeInteger(value) {
      return isInteger(value) && value >= -MAX_SAFE_INTEGER && value <= MAX_SAFE_INTEGER;
    }

    /**
     * Checks if `value` is classified as a `Set` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a set, else `false`.
     * @example
     *
     * _.isSet(new Set);
     * // => true
     *
     * _.isSet(new WeakSet);
     * // => false
     */
    var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

    /**
     * Checks if `value` is classified as a `String` primitive or object.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a string, else `false`.
     * @example
     *
     * _.isString('abc');
     * // => true
     *
     * _.isString(1);
     * // => false
     */
    function isString(value) {
      return typeof value == 'string' ||
        (!isArray(value) && isObjectLike(value) && baseGetTag(value) == stringTag);
    }

    /**
     * Checks if `value` is classified as a `Symbol` primitive or object.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
     * @example
     *
     * _.isSymbol(Symbol.iterator);
     * // => true
     *
     * _.isSymbol('abc');
     * // => false
     */
    function isSymbol(value) {
      return typeof value == 'symbol' ||
        (isObjectLike(value) && baseGetTag(value) == symbolTag);
    }

    /**
     * Checks if `value` is classified as a typed array.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
     * @example
     *
     * _.isTypedArray(new Uint8Array);
     * // => true
     *
     * _.isTypedArray([]);
     * // => false
     */
    var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

    /**
     * Checks if `value` is `undefined`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is `undefined`, else `false`.
     * @example
     *
     * _.isUndefined(void 0);
     * // => true
     *
     * _.isUndefined(null);
     * // => false
     */
    function isUndefined(value) {
      return value === undefined;
    }

    /**
     * Checks if `value` is classified as a `WeakMap` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a weak map, else `false`.
     * @example
     *
     * _.isWeakMap(new WeakMap);
     * // => true
     *
     * _.isWeakMap(new Map);
     * // => false
     */
    function isWeakMap(value) {
      return isObjectLike(value) && getTag(value) == weakMapTag;
    }

    /**
     * Checks if `value` is classified as a `WeakSet` object.
     *
     * @static
     * @memberOf _
     * @since 4.3.0
     * @category Lang
     * @param {*} value The value to check.
     * @returns {boolean} Returns `true` if `value` is a weak set, else `false`.
     * @example
     *
     * _.isWeakSet(new WeakSet);
     * // => true
     *
     * _.isWeakSet(new Set);
     * // => false
     */
    function isWeakSet(value) {
      return isObjectLike(value) && baseGetTag(value) == weakSetTag;
    }

    /**
     * Checks if `value` is less than `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than `other`,
     *  else `false`.
     * @see _.gt
     * @example
     *
     * _.lt(1, 3);
     * // => true
     *
     * _.lt(3, 3);
     * // => false
     *
     * _.lt(3, 1);
     * // => false
     */
    var lt = createRelationalOperation(baseLt);

    /**
     * Checks if `value` is less than or equal to `other`.
     *
     * @static
     * @memberOf _
     * @since 3.9.0
     * @category Lang
     * @param {*} value The value to compare.
     * @param {*} other The other value to compare.
     * @returns {boolean} Returns `true` if `value` is less than or equal to
     *  `other`, else `false`.
     * @see _.gte
     * @example
     *
     * _.lte(1, 3);
     * // => true
     *
     * _.lte(3, 3);
     * // => true
     *
     * _.lte(3, 1);
     * // => false
     */
    var lte = createRelationalOperation(function(value, other) {
      return value <= other;
    });

    /**
     * Converts `value` to an array.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Array} Returns the converted array.
     * @example
     *
     * _.toArray({ 'a': 1, 'b': 2 });
     * // => [1, 2]
     *
     * _.toArray('abc');
     * // => ['a', 'b', 'c']
     *
     * _.toArray(1);
     * // => []
     *
     * _.toArray(null);
     * // => []
     */
    function toArray(value) {
      if (!value) {
        return [];
      }
      if (isArrayLike(value)) {
        return isString(value) ? stringToArray(value) : copyArray(value);
      }
      if (symIterator && value[symIterator]) {
        return iteratorToArray(value[symIterator]());
      }
      var tag = getTag(value),
          func = tag == mapTag ? mapToArray : (tag == setTag ? setToArray : values);

      return func(value);
    }

    /**
     * Converts `value` to a finite number.
     *
     * @static
     * @memberOf _
     * @since 4.12.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted number.
     * @example
     *
     * _.toFinite(3.2);
     * // => 3.2
     *
     * _.toFinite(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toFinite(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toFinite('3.2');
     * // => 3.2
     */
    function toFinite(value) {
      if (!value) {
        return value === 0 ? value : 0;
      }
      value = toNumber(value);
      if (value === INFINITY || value === -INFINITY) {
        var sign = (value < 0 ? -1 : 1);
        return sign * MAX_INTEGER;
      }
      return value === value ? value : 0;
    }

    /**
     * Converts `value` to an integer.
     *
     * **Note:** This method is loosely based on
     * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toInteger(3.2);
     * // => 3
     *
     * _.toInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toInteger(Infinity);
     * // => 1.7976931348623157e+308
     *
     * _.toInteger('3.2');
     * // => 3
     */
    function toInteger(value) {
      var result = toFinite(value),
          remainder = result % 1;

      return result === result ? (remainder ? result - remainder : result) : 0;
    }

    /**
     * Converts `value` to an integer suitable for use as the length of an
     * array-like object.
     *
     * **Note:** This method is based on
     * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toLength(3.2);
     * // => 3
     *
     * _.toLength(Number.MIN_VALUE);
     * // => 0
     *
     * _.toLength(Infinity);
     * // => 4294967295
     *
     * _.toLength('3.2');
     * // => 3
     */
    function toLength(value) {
      return value ? baseClamp(toInteger(value), 0, MAX_ARRAY_LENGTH) : 0;
    }

    /**
     * Converts `value` to a number.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to process.
     * @returns {number} Returns the number.
     * @example
     *
     * _.toNumber(3.2);
     * // => 3.2
     *
     * _.toNumber(Number.MIN_VALUE);
     * // => 5e-324
     *
     * _.toNumber(Infinity);
     * // => Infinity
     *
     * _.toNumber('3.2');
     * // => 3.2
     */
    function toNumber(value) {
      if (typeof value == 'number') {
        return value;
      }
      if (isSymbol(value)) {
        return NAN;
      }
      if (isObject(value)) {
        var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
        value = isObject(other) ? (other + '') : other;
      }
      if (typeof value != 'string') {
        return value === 0 ? value : +value;
      }
      value = baseTrim(value);
      var isBinary = reIsBinary.test(value);
      return (isBinary || reIsOctal.test(value))
        ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
        : (reIsBadHex.test(value) ? NAN : +value);
    }

    /**
     * Converts `value` to a plain object flattening inherited enumerable string
     * keyed properties of `value` to own properties of the plain object.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {Object} Returns the converted plain object.
     * @example
     *
     * function Foo() {
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.assign({ 'a': 1 }, new Foo);
     * // => { 'a': 1, 'b': 2 }
     *
     * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
     * // => { 'a': 1, 'b': 2, 'c': 3 }
     */
    function toPlainObject(value) {
      return copyObject(value, keysIn(value));
    }

    /**
     * Converts `value` to a safe integer. A safe integer can be compared and
     * represented correctly.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.toSafeInteger(3.2);
     * // => 3
     *
     * _.toSafeInteger(Number.MIN_VALUE);
     * // => 0
     *
     * _.toSafeInteger(Infinity);
     * // => 9007199254740991
     *
     * _.toSafeInteger('3.2');
     * // => 3
     */
    function toSafeInteger(value) {
      return value
        ? baseClamp(toInteger(value), -MAX_SAFE_INTEGER, MAX_SAFE_INTEGER)
        : (value === 0 ? value : 0);
    }

    /**
     * Converts `value` to a string. An empty string is returned for `null`
     * and `undefined` values. The sign of `-0` is preserved.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Lang
     * @param {*} value The value to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.toString(null);
     * // => ''
     *
     * _.toString(-0);
     * // => '-0'
     *
     * _.toString([1, 2, 3]);
     * // => '1,2,3'
     */
    function toString(value) {
      return value == null ? '' : baseToString(value);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Assigns own enumerable string keyed properties of source objects to the
     * destination object. Source objects are applied from left to right.
     * Subsequent sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object` and is loosely based on
     * [`Object.assign`](https://mdn.io/Object/assign).
     *
     * @static
     * @memberOf _
     * @since 0.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assignIn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * function Bar() {
     *   this.c = 3;
     * }
     *
     * Foo.prototype.b = 2;
     * Bar.prototype.d = 4;
     *
     * _.assign({ 'a': 0 }, new Foo, new Bar);
     * // => { 'a': 1, 'c': 3 }
     */
    var assign = createAssigner(function(object, source) {
      if (isPrototype(source) || isArrayLike(source)) {
        copyObject(source, keys(source), object);
        return;
      }
      for (var key in source) {
        if (hasOwnProperty.call(source, key)) {
          assignValue(object, key, source[key]);
        }
      }
    });

    /**
     * This method is like `_.assign` except that it iterates over own and
     * inherited source properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extend
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.assign
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     * }
     *
     * function Bar() {
     *   this.c = 3;
     * }
     *
     * Foo.prototype.b = 2;
     * Bar.prototype.d = 4;
     *
     * _.assignIn({ 'a': 0 }, new Foo, new Bar);
     * // => { 'a': 1, 'b': 2, 'c': 3, 'd': 4 }
     */
    var assignIn = createAssigner(function(object, source) {
      copyObject(source, keysIn(source), object);
    });

    /**
     * This method is like `_.assignIn` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias extendWith
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignInWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignInWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keysIn(source), object, customizer);
    });

    /**
     * This method is like `_.assign` except that it accepts `customizer`
     * which is invoked to produce the assigned values. If `customizer` returns
     * `undefined`, assignment is handled by the method instead. The `customizer`
     * is invoked with five arguments: (objValue, srcValue, key, object, source).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @see _.assignInWith
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   return _.isUndefined(objValue) ? srcValue : objValue;
     * }
     *
     * var defaults = _.partialRight(_.assignWith, customizer);
     *
     * defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var assignWith = createAssigner(function(object, source, srcIndex, customizer) {
      copyObject(source, keys(source), object, customizer);
    });

    /**
     * Creates an array of values corresponding to `paths` of `object`.
     *
     * @static
     * @memberOf _
     * @since 1.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Array} Returns the picked values.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }, 4] };
     *
     * _.at(object, ['a[0].b.c', 'a[1]']);
     * // => [3, 4]
     */
    var at = flatRest(baseAt);

    /**
     * Creates an object that inherits from the `prototype` object. If a
     * `properties` object is given, its own enumerable string keyed properties
     * are assigned to the created object.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Object
     * @param {Object} prototype The object to inherit from.
     * @param {Object} [properties] The properties to assign to the object.
     * @returns {Object} Returns the new object.
     * @example
     *
     * function Shape() {
     *   this.x = 0;
     *   this.y = 0;
     * }
     *
     * function Circle() {
     *   Shape.call(this);
     * }
     *
     * Circle.prototype = _.create(Shape.prototype, {
     *   'constructor': Circle
     * });
     *
     * var circle = new Circle;
     * circle instanceof Circle;
     * // => true
     *
     * circle instanceof Shape;
     * // => true
     */
    function create(prototype, properties) {
      var result = baseCreate(prototype);
      return properties == null ? result : baseAssign(result, properties);
    }

    /**
     * Assigns own and inherited enumerable string keyed properties of source
     * objects to the destination object for all destination properties that
     * resolve to `undefined`. Source objects are applied from left to right.
     * Once a property is set, additional values of the same property are ignored.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaultsDeep
     * @example
     *
     * _.defaults({ 'a': 1 }, { 'b': 2 }, { 'a': 3 });
     * // => { 'a': 1, 'b': 2 }
     */
    var defaults = baseRest(function(object, sources) {
      object = Object(object);

      var index = -1;
      var length = sources.length;
      var guard = length > 2 ? sources[2] : undefined;

      if (guard && isIterateeCall(sources[0], sources[1], guard)) {
        length = 1;
      }

      while (++index < length) {
        var source = sources[index];
        var props = keysIn(source);
        var propsIndex = -1;
        var propsLength = props.length;

        while (++propsIndex < propsLength) {
          var key = props[propsIndex];
          var value = object[key];

          if (value === undefined ||
              (eq(value, objectProto[key]) && !hasOwnProperty.call(object, key))) {
            object[key] = source[key];
          }
        }
      }

      return object;
    });

    /**
     * This method is like `_.defaults` except that it recursively assigns
     * default properties.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @see _.defaults
     * @example
     *
     * _.defaultsDeep({ 'a': { 'b': 2 } }, { 'a': { 'b': 1, 'c': 3 } });
     * // => { 'a': { 'b': 2, 'c': 3 } }
     */
    var defaultsDeep = baseRest(function(args) {
      args.push(undefined, customDefaultsMerge);
      return apply(mergeWith, undefined, args);
    });

    /**
     * This method is like `_.find` except that it returns the key of the first
     * element `predicate` returns truthy for instead of the element itself.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findKey(users, function(o) { return o.age < 40; });
     * // => 'barney' (iteration order is not guaranteed)
     *
     * // The `_.matches` iteratee shorthand.
     * _.findKey(users, { 'age': 1, 'active': true });
     * // => 'pebbles'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findKey(users, 'active');
     * // => 'barney'
     */
    function findKey(object, predicate) {
      return baseFindKey(object, getIteratee(predicate, 3), baseForOwn);
    }

    /**
     * This method is like `_.findKey` except that it iterates over elements of
     * a collection in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @param {Function} [predicate=_.identity] The function invoked per iteration.
     * @returns {string|undefined} Returns the key of the matched element,
     *  else `undefined`.
     * @example
     *
     * var users = {
     *   'barney':  { 'age': 36, 'active': true },
     *   'fred':    { 'age': 40, 'active': false },
     *   'pebbles': { 'age': 1,  'active': true }
     * };
     *
     * _.findLastKey(users, function(o) { return o.age < 40; });
     * // => returns 'pebbles' assuming `_.findKey` returns 'barney'
     *
     * // The `_.matches` iteratee shorthand.
     * _.findLastKey(users, { 'age': 36, 'active': true });
     * // => 'barney'
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.findLastKey(users, ['active', false]);
     * // => 'fred'
     *
     * // The `_.property` iteratee shorthand.
     * _.findLastKey(users, 'active');
     * // => 'pebbles'
     */
    function findLastKey(object, predicate) {
      return baseFindKey(object, getIteratee(predicate, 3), baseForOwnRight);
    }

    /**
     * Iterates over own and inherited enumerable string keyed properties of an
     * object and invokes `iteratee` for each property. The iteratee is invoked
     * with three arguments: (value, key, object). Iteratee functions may exit
     * iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forInRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forIn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a', 'b', then 'c' (iteration order is not guaranteed).
     */
    function forIn(object, iteratee) {
      return object == null
        ? object
        : baseFor(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * This method is like `_.forIn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forIn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forInRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'c', 'b', then 'a' assuming `_.forIn` logs 'a', 'b', then 'c'.
     */
    function forInRight(object, iteratee) {
      return object == null
        ? object
        : baseForRight(object, getIteratee(iteratee, 3), keysIn);
    }

    /**
     * Iterates over own enumerable string keyed properties of an object and
     * invokes `iteratee` for each property. The iteratee is invoked with three
     * arguments: (value, key, object). Iteratee functions may exit iteration
     * early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 0.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwnRight
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwn(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'a' then 'b' (iteration order is not guaranteed).
     */
    function forOwn(object, iteratee) {
      return object && baseForOwn(object, getIteratee(iteratee, 3));
    }

    /**
     * This method is like `_.forOwn` except that it iterates over properties of
     * `object` in the opposite order.
     *
     * @static
     * @memberOf _
     * @since 2.0.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns `object`.
     * @see _.forOwn
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.forOwnRight(new Foo, function(value, key) {
     *   console.log(key);
     * });
     * // => Logs 'b' then 'a' assuming `_.forOwn` logs 'a' then 'b'.
     */
    function forOwnRight(object, iteratee) {
      return object && baseForOwnRight(object, getIteratee(iteratee, 3));
    }

    /**
     * Creates an array of function property names from own enumerable properties
     * of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functionsIn
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functions(new Foo);
     * // => ['a', 'b']
     */
    function functions(object) {
      return object == null ? [] : baseFunctions(object, keys(object));
    }

    /**
     * Creates an array of function property names from own and inherited
     * enumerable properties of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to inspect.
     * @returns {Array} Returns the function names.
     * @see _.functions
     * @example
     *
     * function Foo() {
     *   this.a = _.constant('a');
     *   this.b = _.constant('b');
     * }
     *
     * Foo.prototype.c = _.constant('c');
     *
     * _.functionsIn(new Foo);
     * // => ['a', 'b', 'c']
     */
    function functionsIn(object) {
      return object == null ? [] : baseFunctions(object, keysIn(object));
    }

    /**
     * Gets the value at `path` of `object`. If the resolved value is
     * `undefined`, the `defaultValue` is returned in its place.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to get.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.get(object, 'a[0].b.c');
     * // => 3
     *
     * _.get(object, ['a', '0', 'b', 'c']);
     * // => 3
     *
     * _.get(object, 'a.b.c', 'default');
     * // => 'default'
     */
    function get(object, path, defaultValue) {
      var result = object == null ? undefined : baseGet(object, path);
      return result === undefined ? defaultValue : result;
    }

    /**
     * Checks if `path` is a direct property of `object`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = { 'a': { 'b': 2 } };
     * var other = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.has(object, 'a');
     * // => true
     *
     * _.has(object, 'a.b');
     * // => true
     *
     * _.has(object, ['a', 'b']);
     * // => true
     *
     * _.has(other, 'a');
     * // => false
     */
    function has(object, path) {
      return object != null && hasPath(object, path, baseHas);
    }

    /**
     * Checks if `path` is a direct or inherited property of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path to check.
     * @returns {boolean} Returns `true` if `path` exists, else `false`.
     * @example
     *
     * var object = _.create({ 'a': _.create({ 'b': 2 }) });
     *
     * _.hasIn(object, 'a');
     * // => true
     *
     * _.hasIn(object, 'a.b');
     * // => true
     *
     * _.hasIn(object, ['a', 'b']);
     * // => true
     *
     * _.hasIn(object, 'b');
     * // => false
     */
    function hasIn(object, path) {
      return object != null && hasPath(object, path, baseHasIn);
    }

    /**
     * Creates an object composed of the inverted keys and values of `object`.
     * If `object` contains duplicate values, subsequent values overwrite
     * property assignments of previous values.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Object
     * @param {Object} object The object to invert.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invert(object);
     * // => { '1': 'c', '2': 'b' }
     */
    var invert = createInverter(function(result, value, key) {
      if (value != null &&
          typeof value.toString != 'function') {
        value = nativeObjectToString.call(value);
      }

      result[value] = key;
    }, constant(identity));

    /**
     * This method is like `_.invert` except that the inverted object is generated
     * from the results of running each element of `object` thru `iteratee`. The
     * corresponding inverted value of each inverted key is an array of keys
     * responsible for generating the inverted value. The iteratee is invoked
     * with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.1.0
     * @category Object
     * @param {Object} object The object to invert.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {Object} Returns the new inverted object.
     * @example
     *
     * var object = { 'a': 1, 'b': 2, 'c': 1 };
     *
     * _.invertBy(object);
     * // => { '1': ['a', 'c'], '2': ['b'] }
     *
     * _.invertBy(object, function(value) {
     *   return 'group' + value;
     * });
     * // => { 'group1': ['a', 'c'], 'group2': ['b'] }
     */
    var invertBy = createInverter(function(result, value, key) {
      if (value != null &&
          typeof value.toString != 'function') {
        value = nativeObjectToString.call(value);
      }

      if (hasOwnProperty.call(result, value)) {
        result[value].push(key);
      } else {
        result[value] = [key];
      }
    }, getIteratee);

    /**
     * Invokes the method at `path` of `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {*} Returns the result of the invoked method.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': [1, 2, 3, 4] } }] };
     *
     * _.invoke(object, 'a[0].b.c.slice', 1, 3);
     * // => [2, 3]
     */
    var invoke = baseRest(baseInvoke);

    /**
     * Creates an array of the own enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects. See the
     * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
     * for more details.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keys(new Foo);
     * // => ['a', 'b'] (iteration order is not guaranteed)
     *
     * _.keys('hi');
     * // => ['0', '1']
     */
    function keys(object) {
      return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
    }

    /**
     * Creates an array of the own and inherited enumerable property names of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property names.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.keysIn(new Foo);
     * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
     */
    function keysIn(object) {
      return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
    }

    /**
     * The opposite of `_.mapValues`; this method creates an object with the
     * same values as `object` and keys generated by running each own enumerable
     * string keyed property of `object` thru `iteratee`. The iteratee is invoked
     * with three arguments: (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 3.8.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapValues
     * @example
     *
     * _.mapKeys({ 'a': 1, 'b': 2 }, function(value, key) {
     *   return key + value;
     * });
     * // => { 'a1': 1, 'b2': 2 }
     */
    function mapKeys(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        baseAssignValue(result, iteratee(value, key, object), value);
      });
      return result;
    }

    /**
     * Creates an object with the same keys as `object` and values generated
     * by running each own enumerable string keyed property of `object` thru
     * `iteratee`. The iteratee is invoked with three arguments:
     * (value, key, object).
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Object} Returns the new mapped object.
     * @see _.mapKeys
     * @example
     *
     * var users = {
     *   'fred':    { 'user': 'fred',    'age': 40 },
     *   'pebbles': { 'user': 'pebbles', 'age': 1 }
     * };
     *
     * _.mapValues(users, function(o) { return o.age; });
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     *
     * // The `_.property` iteratee shorthand.
     * _.mapValues(users, 'age');
     * // => { 'fred': 40, 'pebbles': 1 } (iteration order is not guaranteed)
     */
    function mapValues(object, iteratee) {
      var result = {};
      iteratee = getIteratee(iteratee, 3);

      baseForOwn(object, function(value, key, object) {
        baseAssignValue(result, key, iteratee(value, key, object));
      });
      return result;
    }

    /**
     * This method is like `_.assign` except that it recursively merges own and
     * inherited enumerable string keyed properties of source objects into the
     * destination object. Source properties that resolve to `undefined` are
     * skipped if a destination value exists. Array and plain object properties
     * are merged recursively. Other objects and value types are overridden by
     * assignment. Source objects are applied from left to right. Subsequent
     * sources overwrite property assignments of previous sources.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 0.5.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} [sources] The source objects.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {
     *   'a': [{ 'b': 2 }, { 'd': 4 }]
     * };
     *
     * var other = {
     *   'a': [{ 'c': 3 }, { 'e': 5 }]
     * };
     *
     * _.merge(object, other);
     * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
     */
    var merge = createAssigner(function(object, source, srcIndex) {
      baseMerge(object, source, srcIndex);
    });

    /**
     * This method is like `_.merge` except that it accepts `customizer` which
     * is invoked to produce the merged values of the destination and source
     * properties. If `customizer` returns `undefined`, merging is handled by the
     * method instead. The `customizer` is invoked with six arguments:
     * (objValue, srcValue, key, object, source, stack).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The destination object.
     * @param {...Object} sources The source objects.
     * @param {Function} customizer The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * function customizer(objValue, srcValue) {
     *   if (_.isArray(objValue)) {
     *     return objValue.concat(srcValue);
     *   }
     * }
     *
     * var object = { 'a': [1], 'b': [2] };
     * var other = { 'a': [3], 'b': [4] };
     *
     * _.mergeWith(object, other, customizer);
     * // => { 'a': [1, 3], 'b': [2, 4] }
     */
    var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
      baseMerge(object, source, srcIndex, customizer);
    });

    /**
     * The opposite of `_.pick`; this method creates an object composed of the
     * own and inherited enumerable property paths of `object` that are not omitted.
     *
     * **Note:** This method is considerably slower than `_.pick`.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [paths] The property paths to omit.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omit(object, ['a', 'c']);
     * // => { 'b': '2' }
     */
    var omit = flatRest(function(object, paths) {
      var result = {};
      if (object == null) {
        return result;
      }
      var isDeep = false;
      paths = arrayMap(paths, function(path) {
        path = castPath(path, object);
        isDeep || (isDeep = path.length > 1);
        return path;
      });
      copyObject(object, getAllKeysIn(object), result);
      if (isDeep) {
        result = baseClone(result, CLONE_DEEP_FLAG | CLONE_FLAT_FLAG | CLONE_SYMBOLS_FLAG, customOmitClone);
      }
      var length = paths.length;
      while (length--) {
        baseUnset(result, paths[length]);
      }
      return result;
    });

    /**
     * The opposite of `_.pickBy`; this method creates an object composed of
     * the own and inherited enumerable string keyed properties of `object` that
     * `predicate` doesn't return truthy for. The predicate is invoked with two
     * arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Function} [predicate=_.identity] The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.omitBy(object, _.isNumber);
     * // => { 'b': '2' }
     */
    function omitBy(object, predicate) {
      return pickBy(object, negate(getIteratee(predicate)));
    }

    /**
     * Creates an object composed of the picked `object` properties.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The source object.
     * @param {...(string|string[])} [paths] The property paths to pick.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pick(object, ['a', 'c']);
     * // => { 'a': 1, 'c': 3 }
     */
    var pick = flatRest(function(object, paths) {
      return object == null ? {} : basePick(object, paths);
    });

    /**
     * Creates an object composed of the `object` properties `predicate` returns
     * truthy for. The predicate is invoked with two arguments: (value, key).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The source object.
     * @param {Function} [predicate=_.identity] The function invoked per property.
     * @returns {Object} Returns the new object.
     * @example
     *
     * var object = { 'a': 1, 'b': '2', 'c': 3 };
     *
     * _.pickBy(object, _.isNumber);
     * // => { 'a': 1, 'c': 3 }
     */
    function pickBy(object, predicate) {
      if (object == null) {
        return {};
      }
      var props = arrayMap(getAllKeysIn(object), function(prop) {
        return [prop];
      });
      predicate = getIteratee(predicate);
      return basePickBy(object, props, function(value, path) {
        return predicate(value, path[0]);
      });
    }

    /**
     * This method is like `_.get` except that if the resolved value is a
     * function it's invoked with the `this` binding of its parent object and
     * its result is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @param {Array|string} path The path of the property to resolve.
     * @param {*} [defaultValue] The value returned for `undefined` resolved values.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c1': 3, 'c2': _.constant(4) } }] };
     *
     * _.result(object, 'a[0].b.c1');
     * // => 3
     *
     * _.result(object, 'a[0].b.c2');
     * // => 4
     *
     * _.result(object, 'a[0].b.c3', 'default');
     * // => 'default'
     *
     * _.result(object, 'a[0].b.c3', _.constant('default'));
     * // => 'default'
     */
    function result(object, path, defaultValue) {
      path = castPath(path, object);

      var index = -1,
          length = path.length;

      // Ensure the loop is entered when path is empty.
      if (!length) {
        length = 1;
        object = undefined;
      }
      while (++index < length) {
        var value = object == null ? undefined : object[toKey(path[index])];
        if (value === undefined) {
          index = length;
          value = defaultValue;
        }
        object = isFunction(value) ? value.call(object) : value;
      }
      return object;
    }

    /**
     * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
     * it's created. Arrays are created for missing index properties while objects
     * are created for all other missing properties. Use `_.setWith` to customize
     * `path` creation.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.set(object, 'a[0].b.c', 4);
     * console.log(object.a[0].b.c);
     * // => 4
     *
     * _.set(object, ['x', '0', 'y', 'z'], 5);
     * console.log(object.x[0].y.z);
     * // => 5
     */
    function set(object, path, value) {
      return object == null ? object : baseSet(object, path, value);
    }

    /**
     * This method is like `_.set` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {*} value The value to set.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.setWith(object, '[0][1]', 'a', Object);
     * // => { '0': { '1': 'a' } }
     */
    function setWith(object, path, value, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseSet(object, path, value, customizer);
    }

    /**
     * Creates an array of own enumerable string keyed-value pairs for `object`
     * which can be consumed by `_.fromPairs`. If `object` is a map or set, its
     * entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entries
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairs(new Foo);
     * // => [['a', 1], ['b', 2]] (iteration order is not guaranteed)
     */
    var toPairs = createToPairs(keys);

    /**
     * Creates an array of own and inherited enumerable string keyed-value pairs
     * for `object` which can be consumed by `_.fromPairs`. If `object` is a map
     * or set, its entries are returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @alias entriesIn
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the key-value pairs.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.toPairsIn(new Foo);
     * // => [['a', 1], ['b', 2], ['c', 3]] (iteration order is not guaranteed)
     */
    var toPairsIn = createToPairs(keysIn);

    /**
     * An alternative to `_.reduce`; this method transforms `object` to a new
     * `accumulator` object which is the result of running each of its own
     * enumerable string keyed properties thru `iteratee`, with each invocation
     * potentially mutating the `accumulator` object. If `accumulator` is not
     * provided, a new object with the same `[[Prototype]]` will be used. The
     * iteratee is invoked with four arguments: (accumulator, value, key, object).
     * Iteratee functions may exit iteration early by explicitly returning `false`.
     *
     * @static
     * @memberOf _
     * @since 1.3.0
     * @category Object
     * @param {Object} object The object to iterate over.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @param {*} [accumulator] The custom accumulator value.
     * @returns {*} Returns the accumulated value.
     * @example
     *
     * _.transform([2, 3, 4], function(result, n) {
     *   result.push(n *= n);
     *   return n % 2 == 0;
     * }, []);
     * // => [4, 9]
     *
     * _.transform({ 'a': 1, 'b': 2, 'c': 1 }, function(result, value, key) {
     *   (result[value] || (result[value] = [])).push(key);
     * }, {});
     * // => { '1': ['a', 'c'], '2': ['b'] }
     */
    function transform(object, iteratee, accumulator) {
      var isArr = isArray(object),
          isArrLike = isArr || isBuffer(object) || isTypedArray(object);

      iteratee = getIteratee(iteratee, 4);
      if (accumulator == null) {
        var Ctor = object && object.constructor;
        if (isArrLike) {
          accumulator = isArr ? new Ctor : [];
        }
        else if (isObject(object)) {
          accumulator = isFunction(Ctor) ? baseCreate(getPrototype(object)) : {};
        }
        else {
          accumulator = {};
        }
      }
      (isArrLike ? arrayEach : baseForOwn)(object, function(value, index, object) {
        return iteratee(accumulator, value, index, object);
      });
      return accumulator;
    }

    /**
     * Removes the property at `path` of `object`.
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to unset.
     * @returns {boolean} Returns `true` if the property is deleted, else `false`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 7 } }] };
     * _.unset(object, 'a[0].b.c');
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     *
     * _.unset(object, ['a', '0', 'b', 'c']);
     * // => true
     *
     * console.log(object);
     * // => { 'a': [{ 'b': {} }] };
     */
    function unset(object, path) {
      return object == null ? true : baseUnset(object, path);
    }

    /**
     * This method is like `_.set` except that accepts `updater` to produce the
     * value to set. Use `_.updateWith` to customize `path` creation. The `updater`
     * is invoked with one argument: (value).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = { 'a': [{ 'b': { 'c': 3 } }] };
     *
     * _.update(object, 'a[0].b.c', function(n) { return n * n; });
     * console.log(object.a[0].b.c);
     * // => 9
     *
     * _.update(object, 'x[0].y.z', function(n) { return n ? n + 1 : 0; });
     * console.log(object.x[0].y.z);
     * // => 0
     */
    function update(object, path, updater) {
      return object == null ? object : baseUpdate(object, path, castFunction(updater));
    }

    /**
     * This method is like `_.update` except that it accepts `customizer` which is
     * invoked to produce the objects of `path`.  If `customizer` returns `undefined`
     * path creation is handled by the method instead. The `customizer` is invoked
     * with three arguments: (nsValue, key, nsObject).
     *
     * **Note:** This method mutates `object`.
     *
     * @static
     * @memberOf _
     * @since 4.6.0
     * @category Object
     * @param {Object} object The object to modify.
     * @param {Array|string} path The path of the property to set.
     * @param {Function} updater The function to produce the updated value.
     * @param {Function} [customizer] The function to customize assigned values.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var object = {};
     *
     * _.updateWith(object, '[0][1]', _.constant('a'), Object);
     * // => { '0': { '1': 'a' } }
     */
    function updateWith(object, path, updater, customizer) {
      customizer = typeof customizer == 'function' ? customizer : undefined;
      return object == null ? object : baseUpdate(object, path, castFunction(updater), customizer);
    }

    /**
     * Creates an array of the own enumerable string keyed property values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.values(new Foo);
     * // => [1, 2] (iteration order is not guaranteed)
     *
     * _.values('hi');
     * // => ['h', 'i']
     */
    function values(object) {
      return object == null ? [] : baseValues(object, keys(object));
    }

    /**
     * Creates an array of the own and inherited enumerable string keyed property
     * values of `object`.
     *
     * **Note:** Non-object values are coerced to objects.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Object
     * @param {Object} object The object to query.
     * @returns {Array} Returns the array of property values.
     * @example
     *
     * function Foo() {
     *   this.a = 1;
     *   this.b = 2;
     * }
     *
     * Foo.prototype.c = 3;
     *
     * _.valuesIn(new Foo);
     * // => [1, 2, 3] (iteration order is not guaranteed)
     */
    function valuesIn(object) {
      return object == null ? [] : baseValues(object, keysIn(object));
    }

    /*------------------------------------------------------------------------*/

    /**
     * Clamps `number` within the inclusive `lower` and `upper` bounds.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Number
     * @param {number} number The number to clamp.
     * @param {number} [lower] The lower bound.
     * @param {number} upper The upper bound.
     * @returns {number} Returns the clamped number.
     * @example
     *
     * _.clamp(-10, -5, 5);
     * // => -5
     *
     * _.clamp(10, -5, 5);
     * // => 5
     */
    function clamp(number, lower, upper) {
      if (upper === undefined) {
        upper = lower;
        lower = undefined;
      }
      if (upper !== undefined) {
        upper = toNumber(upper);
        upper = upper === upper ? upper : 0;
      }
      if (lower !== undefined) {
        lower = toNumber(lower);
        lower = lower === lower ? lower : 0;
      }
      return baseClamp(toNumber(number), lower, upper);
    }

    /**
     * Checks if `n` is between `start` and up to, but not including, `end`. If
     * `end` is not specified, it's set to `start` with `start` then set to `0`.
     * If `start` is greater than `end` the params are swapped to support
     * negative ranges.
     *
     * @static
     * @memberOf _
     * @since 3.3.0
     * @category Number
     * @param {number} number The number to check.
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @returns {boolean} Returns `true` if `number` is in the range, else `false`.
     * @see _.range, _.rangeRight
     * @example
     *
     * _.inRange(3, 2, 4);
     * // => true
     *
     * _.inRange(4, 8);
     * // => true
     *
     * _.inRange(4, 2);
     * // => false
     *
     * _.inRange(2, 2);
     * // => false
     *
     * _.inRange(1.2, 2);
     * // => true
     *
     * _.inRange(5.2, 4);
     * // => false
     *
     * _.inRange(-3, -2, -6);
     * // => true
     */
    function inRange(number, start, end) {
      start = toFinite(start);
      if (end === undefined) {
        end = start;
        start = 0;
      } else {
        end = toFinite(end);
      }
      number = toNumber(number);
      return baseInRange(number, start, end);
    }

    /**
     * Produces a random number between the inclusive `lower` and `upper` bounds.
     * If only one argument is provided a number between `0` and the given number
     * is returned. If `floating` is `true`, or either `lower` or `upper` are
     * floats, a floating-point number is returned instead of an integer.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @memberOf _
     * @since 0.7.0
     * @category Number
     * @param {number} [lower=0] The lower bound.
     * @param {number} [upper=1] The upper bound.
     * @param {boolean} [floating] Specify returning a floating-point number.
     * @returns {number} Returns the random number.
     * @example
     *
     * _.random(0, 5);
     * // => an integer between 0 and 5
     *
     * _.random(5);
     * // => also an integer between 0 and 5
     *
     * _.random(5, true);
     * // => a floating-point number between 0 and 5
     *
     * _.random(1.2, 5.2);
     * // => a floating-point number between 1.2 and 5.2
     */
    function random(lower, upper, floating) {
      if (floating && typeof floating != 'boolean' && isIterateeCall(lower, upper, floating)) {
        upper = floating = undefined;
      }
      if (floating === undefined) {
        if (typeof upper == 'boolean') {
          floating = upper;
          upper = undefined;
        }
        else if (typeof lower == 'boolean') {
          floating = lower;
          lower = undefined;
        }
      }
      if (lower === undefined && upper === undefined) {
        lower = 0;
        upper = 1;
      }
      else {
        lower = toFinite(lower);
        if (upper === undefined) {
          upper = lower;
          lower = 0;
        } else {
          upper = toFinite(upper);
        }
      }
      if (lower > upper) {
        var temp = lower;
        lower = upper;
        upper = temp;
      }
      if (floating || lower % 1 || upper % 1) {
        var rand = nativeRandom();
        return nativeMin(lower + (rand * (upper - lower + freeParseFloat('1e-' + ((rand + '').length - 1)))), upper);
      }
      return baseRandom(lower, upper);
    }

    /*------------------------------------------------------------------------*/

    /**
     * Converts `string` to [camel case](https://en.wikipedia.org/wiki/CamelCase).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the camel cased string.
     * @example
     *
     * _.camelCase('Foo Bar');
     * // => 'fooBar'
     *
     * _.camelCase('--foo-bar--');
     * // => 'fooBar'
     *
     * _.camelCase('__FOO_BAR__');
     * // => 'fooBar'
     */
    var camelCase = createCompounder(function(result, word, index) {
      word = word.toLowerCase();
      return result + (index ? capitalize(word) : word);
    });

    /**
     * Converts the first character of `string` to upper case and the remaining
     * to lower case.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to capitalize.
     * @returns {string} Returns the capitalized string.
     * @example
     *
     * _.capitalize('FRED');
     * // => 'Fred'
     */
    function capitalize(string) {
      return upperFirst(toString(string).toLowerCase());
    }

    /**
     * Deburrs `string` by converting
     * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
     * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
     * letters to basic Latin letters and removing
     * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to deburr.
     * @returns {string} Returns the deburred string.
     * @example
     *
     * _.deburr('déjà vu');
     * // => 'deja vu'
     */
    function deburr(string) {
      string = toString(string);
      return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
    }

    /**
     * Checks if `string` ends with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {string} [target] The string to search for.
     * @param {number} [position=string.length] The position to search up to.
     * @returns {boolean} Returns `true` if `string` ends with `target`,
     *  else `false`.
     * @example
     *
     * _.endsWith('abc', 'c');
     * // => true
     *
     * _.endsWith('abc', 'b');
     * // => false
     *
     * _.endsWith('abc', 'b', 2);
     * // => true
     */
    function endsWith(string, target, position) {
      string = toString(string);
      target = baseToString(target);

      var length = string.length;
      position = position === undefined
        ? length
        : baseClamp(toInteger(position), 0, length);

      var end = position;
      position -= target.length;
      return position >= 0 && string.slice(position, end) == target;
    }

    /**
     * Converts the characters "&", "<", ">", '"', and "'" in `string` to their
     * corresponding HTML entities.
     *
     * **Note:** No other characters are escaped. To escape additional
     * characters use a third-party library like [_he_](https://mths.be/he).
     *
     * Though the ">" character is escaped for symmetry, characters like
     * ">" and "/" don't need escaping in HTML and have no special meaning
     * unless they're part of a tag or unquoted attribute value. See
     * [Mathias Bynens's article](https://mathiasbynens.be/notes/ambiguous-ampersands)
     * (under "semi-related fun fact") for more details.
     *
     * When working with HTML you should always
     * [quote attribute values](http://wonko.com/post/html-escaping) to reduce
     * XSS vectors.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escape('fred, barney, & pebbles');
     * // => 'fred, barney, &amp; pebbles'
     */
    function escape(string) {
      string = toString(string);
      return (string && reHasUnescapedHtml.test(string))
        ? string.replace(reUnescapedHtml, escapeHtmlChar)
        : string;
    }

    /**
     * Escapes the `RegExp` special characters "^", "$", "\", ".", "*", "+",
     * "?", "(", ")", "[", "]", "{", "}", and "|" in `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to escape.
     * @returns {string} Returns the escaped string.
     * @example
     *
     * _.escapeRegExp('[lodash](https://lodash.com/)');
     * // => '\[lodash\]\(https://lodash\.com/\)'
     */
    function escapeRegExp(string) {
      string = toString(string);
      return (string && reHasRegExpChar.test(string))
        ? string.replace(reRegExpChar, '\\$&')
        : string;
    }

    /**
     * Converts `string` to
     * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the kebab cased string.
     * @example
     *
     * _.kebabCase('Foo Bar');
     * // => 'foo-bar'
     *
     * _.kebabCase('fooBar');
     * // => 'foo-bar'
     *
     * _.kebabCase('__FOO_BAR__');
     * // => 'foo-bar'
     */
    var kebabCase = createCompounder(function(result, word, index) {
      return result + (index ? '-' : '') + word.toLowerCase();
    });

    /**
     * Converts `string`, as space separated words, to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.lowerCase('--Foo-Bar--');
     * // => 'foo bar'
     *
     * _.lowerCase('fooBar');
     * // => 'foo bar'
     *
     * _.lowerCase('__FOO_BAR__');
     * // => 'foo bar'
     */
    var lowerCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toLowerCase();
    });

    /**
     * Converts the first character of `string` to lower case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.lowerFirst('Fred');
     * // => 'fred'
     *
     * _.lowerFirst('FRED');
     * // => 'fRED'
     */
    var lowerFirst = createCaseFirst('toLowerCase');

    /**
     * Pads `string` on the left and right sides if it's shorter than `length`.
     * Padding characters are truncated if they can't be evenly divided by `length`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.pad('abc', 8);
     * // => '  abc   '
     *
     * _.pad('abc', 8, '_-');
     * // => '_-abc_-_'
     *
     * _.pad('abc', 3);
     * // => 'abc'
     */
    function pad(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      if (!length || strLength >= length) {
        return string;
      }
      var mid = (length - strLength) / 2;
      return (
        createPadding(nativeFloor(mid), chars) +
        string +
        createPadding(nativeCeil(mid), chars)
      );
    }

    /**
     * Pads `string` on the right side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padEnd('abc', 6);
     * // => 'abc   '
     *
     * _.padEnd('abc', 6, '_-');
     * // => 'abc_-_'
     *
     * _.padEnd('abc', 3);
     * // => 'abc'
     */
    function padEnd(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (string + createPadding(length - strLength, chars))
        : string;
    }

    /**
     * Pads `string` on the left side if it's shorter than `length`. Padding
     * characters are truncated if they exceed `length`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to pad.
     * @param {number} [length=0] The padding length.
     * @param {string} [chars=' '] The string used as padding.
     * @returns {string} Returns the padded string.
     * @example
     *
     * _.padStart('abc', 6);
     * // => '   abc'
     *
     * _.padStart('abc', 6, '_-');
     * // => '_-_abc'
     *
     * _.padStart('abc', 3);
     * // => 'abc'
     */
    function padStart(string, length, chars) {
      string = toString(string);
      length = toInteger(length);

      var strLength = length ? stringSize(string) : 0;
      return (length && strLength < length)
        ? (createPadding(length - strLength, chars) + string)
        : string;
    }

    /**
     * Converts `string` to an integer of the specified radix. If `radix` is
     * `undefined` or `0`, a `radix` of `10` is used unless `value` is a
     * hexadecimal, in which case a `radix` of `16` is used.
     *
     * **Note:** This method aligns with the
     * [ES5 implementation](https://es5.github.io/#x15.1.2.2) of `parseInt`.
     *
     * @static
     * @memberOf _
     * @since 1.1.0
     * @category String
     * @param {string} string The string to convert.
     * @param {number} [radix=10] The radix to interpret `value` by.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {number} Returns the converted integer.
     * @example
     *
     * _.parseInt('08');
     * // => 8
     *
     * _.map(['6', '08', '10'], _.parseInt);
     * // => [6, 8, 10]
     */
    function parseInt(string, radix, guard) {
      if (guard || radix == null) {
        radix = 0;
      } else if (radix) {
        radix = +radix;
      }
      return nativeParseInt(toString(string).replace(reTrimStart, ''), radix || 0);
    }

    /**
     * Repeats the given string `n` times.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to repeat.
     * @param {number} [n=1] The number of times to repeat the string.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the repeated string.
     * @example
     *
     * _.repeat('*', 3);
     * // => '***'
     *
     * _.repeat('abc', 2);
     * // => 'abcabc'
     *
     * _.repeat('abc', 0);
     * // => ''
     */
    function repeat(string, n, guard) {
      if ((guard ? isIterateeCall(string, n, guard) : n === undefined)) {
        n = 1;
      } else {
        n = toInteger(n);
      }
      return baseRepeat(toString(string), n);
    }

    /**
     * Replaces matches for `pattern` in `string` with `replacement`.
     *
     * **Note:** This method is based on
     * [`String#replace`](https://mdn.io/String/replace).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to modify.
     * @param {RegExp|string} pattern The pattern to replace.
     * @param {Function|string} replacement The match replacement.
     * @returns {string} Returns the modified string.
     * @example
     *
     * _.replace('Hi Fred', 'Fred', 'Barney');
     * // => 'Hi Barney'
     */
    function replace() {
      var args = arguments,
          string = toString(args[0]);

      return args.length < 3 ? string : string.replace(args[1], args[2]);
    }

    /**
     * Converts `string` to
     * [snake case](https://en.wikipedia.org/wiki/Snake_case).
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the snake cased string.
     * @example
     *
     * _.snakeCase('Foo Bar');
     * // => 'foo_bar'
     *
     * _.snakeCase('fooBar');
     * // => 'foo_bar'
     *
     * _.snakeCase('--FOO-BAR--');
     * // => 'foo_bar'
     */
    var snakeCase = createCompounder(function(result, word, index) {
      return result + (index ? '_' : '') + word.toLowerCase();
    });

    /**
     * Splits `string` by `separator`.
     *
     * **Note:** This method is based on
     * [`String#split`](https://mdn.io/String/split).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to split.
     * @param {RegExp|string} separator The separator pattern to split by.
     * @param {number} [limit] The length to truncate results to.
     * @returns {Array} Returns the string segments.
     * @example
     *
     * _.split('a-b-c', '-', 2);
     * // => ['a', 'b']
     */
    function split(string, separator, limit) {
      if (limit && typeof limit != 'number' && isIterateeCall(string, separator, limit)) {
        separator = limit = undefined;
      }
      limit = limit === undefined ? MAX_ARRAY_LENGTH : limit >>> 0;
      if (!limit) {
        return [];
      }
      string = toString(string);
      if (string && (
            typeof separator == 'string' ||
            (separator != null && !isRegExp(separator))
          )) {
        separator = baseToString(separator);
        if (!separator && hasUnicode(string)) {
          return castSlice(stringToArray(string), 0, limit);
        }
      }
      return string.split(separator, limit);
    }

    /**
     * Converts `string` to
     * [start case](https://en.wikipedia.org/wiki/Letter_case#Stylistic_or_specialised_usage).
     *
     * @static
     * @memberOf _
     * @since 3.1.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the start cased string.
     * @example
     *
     * _.startCase('--foo-bar--');
     * // => 'Foo Bar'
     *
     * _.startCase('fooBar');
     * // => 'Foo Bar'
     *
     * _.startCase('__FOO_BAR__');
     * // => 'FOO BAR'
     */
    var startCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + upperFirst(word);
    });

    /**
     * Checks if `string` starts with the given target string.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {string} [target] The string to search for.
     * @param {number} [position=0] The position to search from.
     * @returns {boolean} Returns `true` if `string` starts with `target`,
     *  else `false`.
     * @example
     *
     * _.startsWith('abc', 'a');
     * // => true
     *
     * _.startsWith('abc', 'b');
     * // => false
     *
     * _.startsWith('abc', 'b', 1);
     * // => true
     */
    function startsWith(string, target, position) {
      string = toString(string);
      position = position == null
        ? 0
        : baseClamp(toInteger(position), 0, string.length);

      target = baseToString(target);
      return string.slice(position, position + target.length) == target;
    }

    /**
     * Creates a compiled template function that can interpolate data properties
     * in "interpolate" delimiters, HTML-escape interpolated data properties in
     * "escape" delimiters, and execute JavaScript in "evaluate" delimiters. Data
     * properties may be accessed as free variables in the template. If a setting
     * object is given, it takes precedence over `_.templateSettings` values.
     *
     * **Note:** In the development build `_.template` utilizes
     * [sourceURLs](http://www.html5rocks.com/en/tutorials/developertools/sourcemaps/#toc-sourceurl)
     * for easier debugging.
     *
     * For more information on precompiling templates see
     * [lodash's custom builds documentation](https://lodash.com/custom-builds).
     *
     * For more information on Chrome extension sandboxes see
     * [Chrome's extensions documentation](https://developer.chrome.com/extensions/sandboxingEval).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category String
     * @param {string} [string=''] The template string.
     * @param {Object} [options={}] The options object.
     * @param {RegExp} [options.escape=_.templateSettings.escape]
     *  The HTML "escape" delimiter.
     * @param {RegExp} [options.evaluate=_.templateSettings.evaluate]
     *  The "evaluate" delimiter.
     * @param {Object} [options.imports=_.templateSettings.imports]
     *  An object to import into the template as free variables.
     * @param {RegExp} [options.interpolate=_.templateSettings.interpolate]
     *  The "interpolate" delimiter.
     * @param {string} [options.sourceURL='lodash.templateSources[n]']
     *  The sourceURL of the compiled template.
     * @param {string} [options.variable='obj']
     *  The data object variable name.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Function} Returns the compiled template function.
     * @example
     *
     * // Use the "interpolate" delimiter to create a compiled template.
     * var compiled = _.template('hello <%= user %>!');
     * compiled({ 'user': 'fred' });
     * // => 'hello fred!'
     *
     * // Use the HTML "escape" delimiter to escape data property values.
     * var compiled = _.template('<b><%- value %></b>');
     * compiled({ 'value': '<script>' });
     * // => '<b>&lt;script&gt;</b>'
     *
     * // Use the "evaluate" delimiter to execute JavaScript and generate HTML.
     * var compiled = _.template('<% _.forEach(users, function(user) { %><li><%- user %></li><% }); %>');
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the internal `print` function in "evaluate" delimiters.
     * var compiled = _.template('<% print("hello " + user); %>!');
     * compiled({ 'user': 'barney' });
     * // => 'hello barney!'
     *
     * // Use the ES template literal delimiter as an "interpolate" delimiter.
     * // Disable support by replacing the "interpolate" delimiter.
     * var compiled = _.template('hello ${ user }!');
     * compiled({ 'user': 'pebbles' });
     * // => 'hello pebbles!'
     *
     * // Use backslashes to treat delimiters as plain text.
     * var compiled = _.template('<%= "\\<%- value %\\>" %>');
     * compiled({ 'value': 'ignored' });
     * // => '<%- value %>'
     *
     * // Use the `imports` option to import `jQuery` as `jq`.
     * var text = '<% jq.each(users, function(user) { %><li><%- user %></li><% }); %>';
     * var compiled = _.template(text, { 'imports': { 'jq': jQuery } });
     * compiled({ 'users': ['fred', 'barney'] });
     * // => '<li>fred</li><li>barney</li>'
     *
     * // Use the `sourceURL` option to specify a custom sourceURL for the template.
     * var compiled = _.template('hello <%= user %>!', { 'sourceURL': '/basic/greeting.jst' });
     * compiled(data);
     * // => Find the source of "greeting.jst" under the Sources tab or Resources panel of the web inspector.
     *
     * // Use the `variable` option to ensure a with-statement isn't used in the compiled template.
     * var compiled = _.template('hi <%= data.user %>!', { 'variable': 'data' });
     * compiled.source;
     * // => function(data) {
     * //   var __t, __p = '';
     * //   __p += 'hi ' + ((__t = ( data.user )) == null ? '' : __t) + '!';
     * //   return __p;
     * // }
     *
     * // Use custom template delimiters.
     * _.templateSettings.interpolate = /{{([\s\S]+?)}}/g;
     * var compiled = _.template('hello {{ user }}!');
     * compiled({ 'user': 'mustache' });
     * // => 'hello mustache!'
     *
     * // Use the `source` property to inline compiled templates for meaningful
     * // line numbers in error messages and stack traces.
     * fs.writeFileSync(path.join(process.cwd(), 'jst.js'), '\
     *   var JST = {\
     *     "main": ' + _.template(mainText).source + '\
     *   };\
     * ');
     */
    function template(string, options, guard) {
      // Based on John Resig's `tmpl` implementation
      // (http://ejohn.org/blog/javascript-micro-templating/)
      // and Laura Doktorova's doT.js (https://github.com/olado/doT).
      var settings = lodash.templateSettings;

      if (guard && isIterateeCall(string, options, guard)) {
        options = undefined;
      }
      string = toString(string);
      options = assignInWith({}, options, settings, customDefaultsAssignIn);

      var imports = assignInWith({}, options.imports, settings.imports, customDefaultsAssignIn),
          importsKeys = keys(imports),
          importsValues = baseValues(imports, importsKeys);

      var isEscaping,
          isEvaluating,
          index = 0,
          interpolate = options.interpolate || reNoMatch,
          source = "__p += '";

      // Compile the regexp to match each delimiter.
      var reDelimiters = RegExp(
        (options.escape || reNoMatch).source + '|' +
        interpolate.source + '|' +
        (interpolate === reInterpolate ? reEsTemplate : reNoMatch).source + '|' +
        (options.evaluate || reNoMatch).source + '|$'
      , 'g');

      // Use a sourceURL for easier debugging.
      // The sourceURL gets injected into the source that's eval-ed, so be careful
      // to normalize all kinds of whitespace, so e.g. newlines (and unicode versions of it) can't sneak in
      // and escape the comment, thus injecting code that gets evaled.
      var sourceURL = '//# sourceURL=' +
        (hasOwnProperty.call(options, 'sourceURL')
          ? (options.sourceURL + '').replace(/\s/g, ' ')
          : ('lodash.templateSources[' + (++templateCounter) + ']')
        ) + '\n';

      string.replace(reDelimiters, function(match, escapeValue, interpolateValue, esTemplateValue, evaluateValue, offset) {
        interpolateValue || (interpolateValue = esTemplateValue);

        // Escape characters that can't be included in string literals.
        source += string.slice(index, offset).replace(reUnescapedString, escapeStringChar);

        // Replace delimiters with snippets.
        if (escapeValue) {
          isEscaping = true;
          source += "' +\n__e(" + escapeValue + ") +\n'";
        }
        if (evaluateValue) {
          isEvaluating = true;
          source += "';\n" + evaluateValue + ";\n__p += '";
        }
        if (interpolateValue) {
          source += "' +\n((__t = (" + interpolateValue + ")) == null ? '' : __t) +\n'";
        }
        index = offset + match.length;

        // The JS engine embedded in Adobe products needs `match` returned in
        // order to produce the correct `offset` value.
        return match;
      });

      source += "';\n";

      // If `variable` is not specified wrap a with-statement around the generated
      // code to add the data object to the top of the scope chain.
      var variable = hasOwnProperty.call(options, 'variable') && options.variable;
      if (!variable) {
        source = 'with (obj) {\n' + source + '\n}\n';
      }
      // Throw an error if a forbidden character was found in `variable`, to prevent
      // potential command injection attacks.
      else if (reForbiddenIdentifierChars.test(variable)) {
        throw new Error(INVALID_TEMPL_VAR_ERROR_TEXT);
      }

      // Cleanup code by stripping empty strings.
      source = (isEvaluating ? source.replace(reEmptyStringLeading, '') : source)
        .replace(reEmptyStringMiddle, '$1')
        .replace(reEmptyStringTrailing, '$1;');

      // Frame code as the function body.
      source = 'function(' + (variable || 'obj') + ') {\n' +
        (variable
          ? ''
          : 'obj || (obj = {});\n'
        ) +
        "var __t, __p = ''" +
        (isEscaping
           ? ', __e = _.escape'
           : ''
        ) +
        (isEvaluating
          ? ', __j = Array.prototype.join;\n' +
            "function print() { __p += __j.call(arguments, '') }\n"
          : ';\n'
        ) +
        source +
        'return __p\n}';

      var result = attempt(function() {
        return Function(importsKeys, sourceURL + 'return ' + source)
          .apply(undefined, importsValues);
      });

      // Provide the compiled function's source by its `toString` method or
      // the `source` property as a convenience for inlining compiled templates.
      result.source = source;
      if (isError(result)) {
        throw result;
      }
      return result;
    }

    /**
     * Converts `string`, as a whole, to lower case just like
     * [String#toLowerCase](https://mdn.io/toLowerCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the lower cased string.
     * @example
     *
     * _.toLower('--Foo-Bar--');
     * // => '--foo-bar--'
     *
     * _.toLower('fooBar');
     * // => 'foobar'
     *
     * _.toLower('__FOO_BAR__');
     * // => '__foo_bar__'
     */
    function toLower(value) {
      return toString(value).toLowerCase();
    }

    /**
     * Converts `string`, as a whole, to upper case just like
     * [String#toUpperCase](https://mdn.io/toUpperCase).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.toUpper('--foo-bar--');
     * // => '--FOO-BAR--'
     *
     * _.toUpper('fooBar');
     * // => 'FOOBAR'
     *
     * _.toUpper('__foo_bar__');
     * // => '__FOO_BAR__'
     */
    function toUpper(value) {
      return toString(value).toUpperCase();
    }

    /**
     * Removes leading and trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trim('  abc  ');
     * // => 'abc'
     *
     * _.trim('-_-abc-_-', '_-');
     * // => 'abc'
     *
     * _.map(['  foo  ', '  bar  '], _.trim);
     * // => ['foo', 'bar']
     */
    function trim(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return baseTrim(string);
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          chrSymbols = stringToArray(chars),
          start = charsStartIndex(strSymbols, chrSymbols),
          end = charsEndIndex(strSymbols, chrSymbols) + 1;

      return castSlice(strSymbols, start, end).join('');
    }

    /**
     * Removes trailing whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimEnd('  abc  ');
     * // => '  abc'
     *
     * _.trimEnd('-_-abc-_-', '_-');
     * // => '-_-abc'
     */
    function trimEnd(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.slice(0, trimmedEndIndex(string) + 1);
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          end = charsEndIndex(strSymbols, stringToArray(chars)) + 1;

      return castSlice(strSymbols, 0, end).join('');
    }

    /**
     * Removes leading whitespace or specified characters from `string`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to trim.
     * @param {string} [chars=whitespace] The characters to trim.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {string} Returns the trimmed string.
     * @example
     *
     * _.trimStart('  abc  ');
     * // => 'abc  '
     *
     * _.trimStart('-_-abc-_-', '_-');
     * // => 'abc-_-'
     */
    function trimStart(string, chars, guard) {
      string = toString(string);
      if (string && (guard || chars === undefined)) {
        return string.replace(reTrimStart, '');
      }
      if (!string || !(chars = baseToString(chars))) {
        return string;
      }
      var strSymbols = stringToArray(string),
          start = charsStartIndex(strSymbols, stringToArray(chars));

      return castSlice(strSymbols, start).join('');
    }

    /**
     * Truncates `string` if it's longer than the given maximum string length.
     * The last characters of the truncated string are replaced with the omission
     * string which defaults to "...".
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to truncate.
     * @param {Object} [options={}] The options object.
     * @param {number} [options.length=30] The maximum string length.
     * @param {string} [options.omission='...'] The string to indicate text is omitted.
     * @param {RegExp|string} [options.separator] The separator pattern to truncate to.
     * @returns {string} Returns the truncated string.
     * @example
     *
     * _.truncate('hi-diddly-ho there, neighborino');
     * // => 'hi-diddly-ho there, neighbo...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': ' '
     * });
     * // => 'hi-diddly-ho there,...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'length': 24,
     *   'separator': /,? +/
     * });
     * // => 'hi-diddly-ho there...'
     *
     * _.truncate('hi-diddly-ho there, neighborino', {
     *   'omission': ' [...]'
     * });
     * // => 'hi-diddly-ho there, neig [...]'
     */
    function truncate(string, options) {
      var length = DEFAULT_TRUNC_LENGTH,
          omission = DEFAULT_TRUNC_OMISSION;

      if (isObject(options)) {
        var separator = 'separator' in options ? options.separator : separator;
        length = 'length' in options ? toInteger(options.length) : length;
        omission = 'omission' in options ? baseToString(options.omission) : omission;
      }
      string = toString(string);

      var strLength = string.length;
      if (hasUnicode(string)) {
        var strSymbols = stringToArray(string);
        strLength = strSymbols.length;
      }
      if (length >= strLength) {
        return string;
      }
      var end = length - stringSize(omission);
      if (end < 1) {
        return omission;
      }
      var result = strSymbols
        ? castSlice(strSymbols, 0, end).join('')
        : string.slice(0, end);

      if (separator === undefined) {
        return result + omission;
      }
      if (strSymbols) {
        end += (result.length - end);
      }
      if (isRegExp(separator)) {
        if (string.slice(end).search(separator)) {
          var match,
              substring = result;

          if (!separator.global) {
            separator = RegExp(separator.source, toString(reFlags.exec(separator)) + 'g');
          }
          separator.lastIndex = 0;
          while ((match = separator.exec(substring))) {
            var newEnd = match.index;
          }
          result = result.slice(0, newEnd === undefined ? end : newEnd);
        }
      } else if (string.indexOf(baseToString(separator), end) != end) {
        var index = result.lastIndexOf(separator);
        if (index > -1) {
          result = result.slice(0, index);
        }
      }
      return result + omission;
    }

    /**
     * The inverse of `_.escape`; this method converts the HTML entities
     * `&amp;`, `&lt;`, `&gt;`, `&quot;`, and `&#39;` in `string` to
     * their corresponding characters.
     *
     * **Note:** No other HTML entities are unescaped. To unescape additional
     * HTML entities use a third-party library like [_he_](https://mths.be/he).
     *
     * @static
     * @memberOf _
     * @since 0.6.0
     * @category String
     * @param {string} [string=''] The string to unescape.
     * @returns {string} Returns the unescaped string.
     * @example
     *
     * _.unescape('fred, barney, &amp; pebbles');
     * // => 'fred, barney, & pebbles'
     */
    function unescape(string) {
      string = toString(string);
      return (string && reHasEscapedHtml.test(string))
        ? string.replace(reEscapedHtml, unescapeHtmlChar)
        : string;
    }

    /**
     * Converts `string`, as space separated words, to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the upper cased string.
     * @example
     *
     * _.upperCase('--foo-bar');
     * // => 'FOO BAR'
     *
     * _.upperCase('fooBar');
     * // => 'FOO BAR'
     *
     * _.upperCase('__foo_bar__');
     * // => 'FOO BAR'
     */
    var upperCase = createCompounder(function(result, word, index) {
      return result + (index ? ' ' : '') + word.toUpperCase();
    });

    /**
     * Converts the first character of `string` to upper case.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category String
     * @param {string} [string=''] The string to convert.
     * @returns {string} Returns the converted string.
     * @example
     *
     * _.upperFirst('fred');
     * // => 'Fred'
     *
     * _.upperFirst('FRED');
     * // => 'FRED'
     */
    var upperFirst = createCaseFirst('toUpperCase');

    /**
     * Splits `string` into an array of its words.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category String
     * @param {string} [string=''] The string to inspect.
     * @param {RegExp|string} [pattern] The pattern to match words.
     * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
     * @returns {Array} Returns the words of `string`.
     * @example
     *
     * _.words('fred, barney, & pebbles');
     * // => ['fred', 'barney', 'pebbles']
     *
     * _.words('fred, barney, & pebbles', /[^, ]+/g);
     * // => ['fred', 'barney', '&', 'pebbles']
     */
    function words(string, pattern, guard) {
      string = toString(string);
      pattern = guard ? undefined : pattern;

      if (pattern === undefined) {
        return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
      }
      return string.match(pattern) || [];
    }

    /*------------------------------------------------------------------------*/

    /**
     * Attempts to invoke `func`, returning either the result or the caught error
     * object. Any additional arguments are provided to `func` when it's invoked.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Function} func The function to attempt.
     * @param {...*} [args] The arguments to invoke `func` with.
     * @returns {*} Returns the `func` result or error object.
     * @example
     *
     * // Avoid throwing errors for invalid selectors.
     * var elements = _.attempt(function(selector) {
     *   return document.querySelectorAll(selector);
     * }, '>_>');
     *
     * if (_.isError(elements)) {
     *   elements = [];
     * }
     */
    var attempt = baseRest(function(func, args) {
      try {
        return apply(func, undefined, args);
      } catch (e) {
        return isError(e) ? e : new Error(e);
      }
    });

    /**
     * Binds methods of an object to the object itself, overwriting the existing
     * method.
     *
     * **Note:** This method doesn't set the "length" property of bound functions.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Object} object The object to bind and assign the bound methods to.
     * @param {...(string|string[])} methodNames The object method names to bind.
     * @returns {Object} Returns `object`.
     * @example
     *
     * var view = {
     *   'label': 'docs',
     *   'click': function() {
     *     console.log('clicked ' + this.label);
     *   }
     * };
     *
     * _.bindAll(view, ['click']);
     * jQuery(element).on('click', view.click);
     * // => Logs 'clicked docs' when clicked.
     */
    var bindAll = flatRest(function(object, methodNames) {
      arrayEach(methodNames, function(key) {
        key = toKey(key);
        baseAssignValue(object, key, bind(object[key], object));
      });
      return object;
    });

    /**
     * Creates a function that iterates over `pairs` and invokes the corresponding
     * function of the first predicate to return truthy. The predicate-function
     * pairs are invoked with the `this` binding and arguments of the created
     * function.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Array} pairs The predicate-function pairs.
     * @returns {Function} Returns the new composite function.
     * @example
     *
     * var func = _.cond([
     *   [_.matches({ 'a': 1 }),           _.constant('matches A')],
     *   [_.conforms({ 'b': _.isNumber }), _.constant('matches B')],
     *   [_.stubTrue,                      _.constant('no match')]
     * ]);
     *
     * func({ 'a': 1, 'b': 2 });
     * // => 'matches A'
     *
     * func({ 'a': 0, 'b': 1 });
     * // => 'matches B'
     *
     * func({ 'a': '1', 'b': '2' });
     * // => 'no match'
     */
    function cond(pairs) {
      var length = pairs == null ? 0 : pairs.length,
          toIteratee = getIteratee();

      pairs = !length ? [] : arrayMap(pairs, function(pair) {
        if (typeof pair[1] != 'function') {
          throw new TypeError(FUNC_ERROR_TEXT);
        }
        return [toIteratee(pair[0]), pair[1]];
      });

      return baseRest(function(args) {
        var index = -1;
        while (++index < length) {
          var pair = pairs[index];
          if (apply(pair[0], this, args)) {
            return apply(pair[1], this, args);
          }
        }
      });
    }

    /**
     * Creates a function that invokes the predicate properties of `source` with
     * the corresponding property values of a given object, returning `true` if
     * all predicates return truthy, else `false`.
     *
     * **Note:** The created function is equivalent to `_.conformsTo` with
     * `source` partially applied.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {Object} source The object of property predicates to conform to.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 2, 'b': 1 },
     *   { 'a': 1, 'b': 2 }
     * ];
     *
     * _.filter(objects, _.conforms({ 'b': function(n) { return n > 1; } }));
     * // => [{ 'a': 1, 'b': 2 }]
     */
    function conforms(source) {
      return baseConforms(baseClone(source, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that returns `value`.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {*} value The value to return from the new function.
     * @returns {Function} Returns the new constant function.
     * @example
     *
     * var objects = _.times(2, _.constant({ 'a': 1 }));
     *
     * console.log(objects);
     * // => [{ 'a': 1 }, { 'a': 1 }]
     *
     * console.log(objects[0] === objects[1]);
     * // => true
     */
    function constant(value) {
      return function() {
        return value;
      };
    }

    /**
     * Checks `value` to determine whether a default value should be returned in
     * its place. The `defaultValue` is returned if `value` is `NaN`, `null`,
     * or `undefined`.
     *
     * @static
     * @memberOf _
     * @since 4.14.0
     * @category Util
     * @param {*} value The value to check.
     * @param {*} defaultValue The default value.
     * @returns {*} Returns the resolved value.
     * @example
     *
     * _.defaultTo(1, 10);
     * // => 1
     *
     * _.defaultTo(undefined, 10);
     * // => 10
     */
    function defaultTo(value, defaultValue) {
      return (value == null || value !== value) ? defaultValue : value;
    }

    /**
     * Creates a function that returns the result of invoking the given functions
     * with the `this` binding of the created function, where each successive
     * invocation is supplied the return value of the previous.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {...(Function|Function[])} [funcs] The functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flowRight
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flow([_.add, square]);
     * addSquare(1, 2);
     * // => 9
     */
    var flow = createFlow();

    /**
     * This method is like `_.flow` except that it creates a function that
     * invokes the given functions from right to left.
     *
     * @static
     * @since 3.0.0
     * @memberOf _
     * @category Util
     * @param {...(Function|Function[])} [funcs] The functions to invoke.
     * @returns {Function} Returns the new composite function.
     * @see _.flow
     * @example
     *
     * function square(n) {
     *   return n * n;
     * }
     *
     * var addSquare = _.flowRight([square, _.add]);
     * addSquare(1, 2);
     * // => 9
     */
    var flowRight = createFlow(true);

    /**
     * This method returns the first argument it receives.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {*} value Any value.
     * @returns {*} Returns `value`.
     * @example
     *
     * var object = { 'a': 1 };
     *
     * console.log(_.identity(object) === object);
     * // => true
     */
    function identity(value) {
      return value;
    }

    /**
     * Creates a function that invokes `func` with the arguments of the created
     * function. If `func` is a property name, the created function returns the
     * property value for a given element. If `func` is an array or object, the
     * created function returns `true` for elements that contain the equivalent
     * source properties, otherwise it returns `false`.
     *
     * @static
     * @since 4.0.0
     * @memberOf _
     * @category Util
     * @param {*} [func=_.identity] The value to convert to a callback.
     * @returns {Function} Returns the callback.
     * @example
     *
     * var users = [
     *   { 'user': 'barney', 'age': 36, 'active': true },
     *   { 'user': 'fred',   'age': 40, 'active': false }
     * ];
     *
     * // The `_.matches` iteratee shorthand.
     * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
     * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
     *
     * // The `_.matchesProperty` iteratee shorthand.
     * _.filter(users, _.iteratee(['user', 'fred']));
     * // => [{ 'user': 'fred', 'age': 40 }]
     *
     * // The `_.property` iteratee shorthand.
     * _.map(users, _.iteratee('user'));
     * // => ['barney', 'fred']
     *
     * // Create custom iteratee shorthands.
     * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
     *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
     *     return func.test(string);
     *   };
     * });
     *
     * _.filter(['abc', 'def'], /ef/);
     * // => ['def']
     */
    function iteratee(func) {
      return baseIteratee(typeof func == 'function' ? func : baseClone(func, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that performs a partial deep comparison between a given
     * object and `source`, returning `true` if the given object has equivalent
     * property values, else `false`.
     *
     * **Note:** The created function is equivalent to `_.isMatch` with `source`
     * partially applied.
     *
     * Partial comparisons will match empty array and empty object `source`
     * values against any array or object value, respectively. See `_.isEqual`
     * for a list of supported value comparisons.
     *
     * **Note:** Multiple values can be checked by combining several matchers
     * using `_.overSome`
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} source The object of property values to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 1, 'b': 2, 'c': 3 },
     *   { 'a': 4, 'b': 5, 'c': 6 }
     * ];
     *
     * _.filter(objects, _.matches({ 'a': 4, 'c': 6 }));
     * // => [{ 'a': 4, 'b': 5, 'c': 6 }]
     *
     * // Checking for several possible values
     * _.filter(objects, _.overSome([_.matches({ 'a': 1 }), _.matches({ 'a': 4 })]));
     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
     */
    function matches(source) {
      return baseMatches(baseClone(source, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that performs a partial deep comparison between the
     * value at `path` of a given object to `srcValue`, returning `true` if the
     * object value is equivalent, else `false`.
     *
     * **Note:** Partial comparisons will match empty array and empty object
     * `srcValue` values against any array or object value, respectively. See
     * `_.isEqual` for a list of supported value comparisons.
     *
     * **Note:** Multiple values can be checked by combining several matchers
     * using `_.overSome`
     *
     * @static
     * @memberOf _
     * @since 3.2.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @param {*} srcValue The value to match.
     * @returns {Function} Returns the new spec function.
     * @example
     *
     * var objects = [
     *   { 'a': 1, 'b': 2, 'c': 3 },
     *   { 'a': 4, 'b': 5, 'c': 6 }
     * ];
     *
     * _.find(objects, _.matchesProperty('a', 4));
     * // => { 'a': 4, 'b': 5, 'c': 6 }
     *
     * // Checking for several possible values
     * _.filter(objects, _.overSome([_.matchesProperty('a', 1), _.matchesProperty('a', 4)]));
     * // => [{ 'a': 1, 'b': 2, 'c': 3 }, { 'a': 4, 'b': 5, 'c': 6 }]
     */
    function matchesProperty(path, srcValue) {
      return baseMatchesProperty(path, baseClone(srcValue, CLONE_DEEP_FLAG));
    }

    /**
     * Creates a function that invokes the method at `path` of a given object.
     * Any additional arguments are provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Array|string} path The path of the method to invoke.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': _.constant(2) } },
     *   { 'a': { 'b': _.constant(1) } }
     * ];
     *
     * _.map(objects, _.method('a.b'));
     * // => [2, 1]
     *
     * _.map(objects, _.method(['a', 'b']));
     * // => [2, 1]
     */
    var method = baseRest(function(path, args) {
      return function(object) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * The opposite of `_.method`; this method creates a function that invokes
     * the method at a given path of `object`. Any additional arguments are
     * provided to the invoked method.
     *
     * @static
     * @memberOf _
     * @since 3.7.0
     * @category Util
     * @param {Object} object The object to query.
     * @param {...*} [args] The arguments to invoke the method with.
     * @returns {Function} Returns the new invoker function.
     * @example
     *
     * var array = _.times(3, _.constant),
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.methodOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.methodOf(object));
     * // => [2, 0]
     */
    var methodOf = baseRest(function(object, args) {
      return function(path) {
        return baseInvoke(object, path, args);
      };
    });

    /**
     * Adds all own enumerable string keyed function properties of a source
     * object to the destination object. If `object` is a function, then methods
     * are added to its prototype as well.
     *
     * **Note:** Use `_.runInContext` to create a pristine `lodash` function to
     * avoid conflicts caused by modifying the original.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {Function|Object} [object=lodash] The destination object.
     * @param {Object} source The object of functions to add.
     * @param {Object} [options={}] The options object.
     * @param {boolean} [options.chain=true] Specify whether mixins are chainable.
     * @returns {Function|Object} Returns `object`.
     * @example
     *
     * function vowels(string) {
     *   return _.filter(string, function(v) {
     *     return /[aeiou]/i.test(v);
     *   });
     * }
     *
     * _.mixin({ 'vowels': vowels });
     * _.vowels('fred');
     * // => ['e']
     *
     * _('fred').vowels().value();
     * // => ['e']
     *
     * _.mixin({ 'vowels': vowels }, { 'chain': false });
     * _('fred').vowels();
     * // => ['e']
     */
    function mixin(object, source, options) {
      var props = keys(source),
          methodNames = baseFunctions(source, props);

      if (options == null &&
          !(isObject(source) && (methodNames.length || !props.length))) {
        options = source;
        source = object;
        object = this;
        methodNames = baseFunctions(source, keys(source));
      }
      var chain = !(isObject(options) && 'chain' in options) || !!options.chain,
          isFunc = isFunction(object);

      arrayEach(methodNames, function(methodName) {
        var func = source[methodName];
        object[methodName] = func;
        if (isFunc) {
          object.prototype[methodName] = function() {
            var chainAll = this.__chain__;
            if (chain || chainAll) {
              var result = object(this.__wrapped__),
                  actions = result.__actions__ = copyArray(this.__actions__);

              actions.push({ 'func': func, 'args': arguments, 'thisArg': object });
              result.__chain__ = chainAll;
              return result;
            }
            return func.apply(object, arrayPush([this.value()], arguments));
          };
        }
      });

      return object;
    }

    /**
     * Reverts the `_` variable to its previous value and returns a reference to
     * the `lodash` function.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @returns {Function} Returns the `lodash` function.
     * @example
     *
     * var lodash = _.noConflict();
     */
    function noConflict() {
      if (root._ === this) {
        root._ = oldDash;
      }
      return this;
    }

    /**
     * This method returns `undefined`.
     *
     * @static
     * @memberOf _
     * @since 2.3.0
     * @category Util
     * @example
     *
     * _.times(2, _.noop);
     * // => [undefined, undefined]
     */
    function noop() {
      // No operation performed.
    }

    /**
     * Creates a function that gets the argument at index `n`. If `n` is negative,
     * the nth argument from the end is returned.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [n=0] The index of the argument to return.
     * @returns {Function} Returns the new pass-thru function.
     * @example
     *
     * var func = _.nthArg(1);
     * func('a', 'b', 'c', 'd');
     * // => 'b'
     *
     * var func = _.nthArg(-2);
     * func('a', 'b', 'c', 'd');
     * // => 'c'
     */
    function nthArg(n) {
      n = toInteger(n);
      return baseRest(function(args) {
        return baseNth(args, n);
      });
    }

    /**
     * Creates a function that invokes `iteratees` with the arguments it receives
     * and returns their results.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [iteratees=[_.identity]]
     *  The iteratees to invoke.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.over([Math.max, Math.min]);
     *
     * func(1, 2, 3, 4);
     * // => [4, 1]
     */
    var over = createOver(arrayMap);

    /**
     * Creates a function that checks if **all** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * Following shorthands are possible for providing predicates.
     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [predicates=[_.identity]]
     *  The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overEvery([Boolean, isFinite]);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => false
     *
     * func(NaN);
     * // => false
     */
    var overEvery = createOver(arrayEvery);

    /**
     * Creates a function that checks if **any** of the `predicates` return
     * truthy when invoked with the arguments it receives.
     *
     * Following shorthands are possible for providing predicates.
     * Pass an `Object` and it will be used as an parameter for `_.matches` to create the predicate.
     * Pass an `Array` of parameters for `_.matchesProperty` and the predicate will be created using them.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {...(Function|Function[])} [predicates=[_.identity]]
     *  The predicates to check.
     * @returns {Function} Returns the new function.
     * @example
     *
     * var func = _.overSome([Boolean, isFinite]);
     *
     * func('1');
     * // => true
     *
     * func(null);
     * // => true
     *
     * func(NaN);
     * // => false
     *
     * var matchesFunc = _.overSome([{ 'a': 1 }, { 'a': 2 }])
     * var matchesPropertyFunc = _.overSome([['a', 1], ['a', 2]])
     */
    var overSome = createOver(arraySome);

    /**
     * Creates a function that returns the value at `path` of a given object.
     *
     * @static
     * @memberOf _
     * @since 2.4.0
     * @category Util
     * @param {Array|string} path The path of the property to get.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var objects = [
     *   { 'a': { 'b': 2 } },
     *   { 'a': { 'b': 1 } }
     * ];
     *
     * _.map(objects, _.property('a.b'));
     * // => [2, 1]
     *
     * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
     * // => [1, 2]
     */
    function property(path) {
      return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
    }

    /**
     * The opposite of `_.property`; this method creates a function that returns
     * the value at a given path of `object`.
     *
     * @static
     * @memberOf _
     * @since 3.0.0
     * @category Util
     * @param {Object} object The object to query.
     * @returns {Function} Returns the new accessor function.
     * @example
     *
     * var array = [0, 1, 2],
     *     object = { 'a': array, 'b': array, 'c': array };
     *
     * _.map(['a[2]', 'c[0]'], _.propertyOf(object));
     * // => [2, 0]
     *
     * _.map([['a', '2'], ['c', '0']], _.propertyOf(object));
     * // => [2, 0]
     */
    function propertyOf(object) {
      return function(path) {
        return object == null ? undefined : baseGet(object, path);
      };
    }

    /**
     * Creates an array of numbers (positive and/or negative) progressing from
     * `start` up to, but not including, `end`. A step of `-1` is used if a negative
     * `start` is specified without an `end` or `step`. If `end` is not specified,
     * it's set to `start` with `start` then set to `0`.
     *
     * **Note:** JavaScript follows the IEEE-754 standard for resolving
     * floating-point values which can produce unexpected results.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.rangeRight
     * @example
     *
     * _.range(4);
     * // => [0, 1, 2, 3]
     *
     * _.range(-4);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 5);
     * // => [1, 2, 3, 4]
     *
     * _.range(0, 20, 5);
     * // => [0, 5, 10, 15]
     *
     * _.range(0, -4, -1);
     * // => [0, -1, -2, -3]
     *
     * _.range(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.range(0);
     * // => []
     */
    var range = createRange();

    /**
     * This method is like `_.range` except that it populates values in
     * descending order.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {number} [start=0] The start of the range.
     * @param {number} end The end of the range.
     * @param {number} [step=1] The value to increment or decrement by.
     * @returns {Array} Returns the range of numbers.
     * @see _.inRange, _.range
     * @example
     *
     * _.rangeRight(4);
     * // => [3, 2, 1, 0]
     *
     * _.rangeRight(-4);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 5);
     * // => [4, 3, 2, 1]
     *
     * _.rangeRight(0, 20, 5);
     * // => [15, 10, 5, 0]
     *
     * _.rangeRight(0, -4, -1);
     * // => [-3, -2, -1, 0]
     *
     * _.rangeRight(1, 4, 0);
     * // => [1, 1, 1]
     *
     * _.rangeRight(0);
     * // => []
     */
    var rangeRight = createRange(true);

    /**
     * This method returns a new empty array.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {Array} Returns the new empty array.
     * @example
     *
     * var arrays = _.times(2, _.stubArray);
     *
     * console.log(arrays);
     * // => [[], []]
     *
     * console.log(arrays[0] === arrays[1]);
     * // => false
     */
    function stubArray() {
      return [];
    }

    /**
     * This method returns `false`.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {boolean} Returns `false`.
     * @example
     *
     * _.times(2, _.stubFalse);
     * // => [false, false]
     */
    function stubFalse() {
      return false;
    }

    /**
     * This method returns a new empty object.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {Object} Returns the new empty object.
     * @example
     *
     * var objects = _.times(2, _.stubObject);
     *
     * console.log(objects);
     * // => [{}, {}]
     *
     * console.log(objects[0] === objects[1]);
     * // => false
     */
    function stubObject() {
      return {};
    }

    /**
     * This method returns an empty string.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {string} Returns the empty string.
     * @example
     *
     * _.times(2, _.stubString);
     * // => ['', '']
     */
    function stubString() {
      return '';
    }

    /**
     * This method returns `true`.
     *
     * @static
     * @memberOf _
     * @since 4.13.0
     * @category Util
     * @returns {boolean} Returns `true`.
     * @example
     *
     * _.times(2, _.stubTrue);
     * // => [true, true]
     */
    function stubTrue() {
      return true;
    }

    /**
     * Invokes the iteratee `n` times, returning an array of the results of
     * each invocation. The iteratee is invoked with one argument; (index).
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {number} n The number of times to invoke `iteratee`.
     * @param {Function} [iteratee=_.identity] The function invoked per iteration.
     * @returns {Array} Returns the array of results.
     * @example
     *
     * _.times(3, String);
     * // => ['0', '1', '2']
     *
     *  _.times(4, _.constant(0));
     * // => [0, 0, 0, 0]
     */
    function times(n, iteratee) {
      n = toInteger(n);
      if (n < 1 || n > MAX_SAFE_INTEGER) {
        return [];
      }
      var index = MAX_ARRAY_LENGTH,
          length = nativeMin(n, MAX_ARRAY_LENGTH);

      iteratee = getIteratee(iteratee);
      n -= MAX_ARRAY_LENGTH;

      var result = baseTimes(length, iteratee);
      while (++index < n) {
        iteratee(index);
      }
      return result;
    }

    /**
     * Converts `value` to a property path array.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Util
     * @param {*} value The value to convert.
     * @returns {Array} Returns the new property path array.
     * @example
     *
     * _.toPath('a.b.c');
     * // => ['a', 'b', 'c']
     *
     * _.toPath('a[0].b.c');
     * // => ['a', '0', 'b', 'c']
     */
    function toPath(value) {
      if (isArray(value)) {
        return arrayMap(value, toKey);
      }
      return isSymbol(value) ? [value] : copyArray(stringToPath(toString(value)));
    }

    /**
     * Generates a unique ID. If `prefix` is given, the ID is appended to it.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Util
     * @param {string} [prefix=''] The value to prefix the ID with.
     * @returns {string} Returns the unique ID.
     * @example
     *
     * _.uniqueId('contact_');
     * // => 'contact_104'
     *
     * _.uniqueId();
     * // => '105'
     */
    function uniqueId(prefix) {
      var id = ++idCounter;
      return toString(prefix) + id;
    }

    /*------------------------------------------------------------------------*/

    /**
     * Adds two numbers.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {number} augend The first number in an addition.
     * @param {number} addend The second number in an addition.
     * @returns {number} Returns the total.
     * @example
     *
     * _.add(6, 4);
     * // => 10
     */
    var add = createMathOperation(function(augend, addend) {
      return augend + addend;
    }, 0);

    /**
     * Computes `number` rounded up to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round up.
     * @param {number} [precision=0] The precision to round up to.
     * @returns {number} Returns the rounded up number.
     * @example
     *
     * _.ceil(4.006);
     * // => 5
     *
     * _.ceil(6.004, 2);
     * // => 6.01
     *
     * _.ceil(6040, -2);
     * // => 6100
     */
    var ceil = createRound('ceil');

    /**
     * Divide two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} dividend The first number in a division.
     * @param {number} divisor The second number in a division.
     * @returns {number} Returns the quotient.
     * @example
     *
     * _.divide(6, 4);
     * // => 1.5
     */
    var divide = createMathOperation(function(dividend, divisor) {
      return dividend / divisor;
    }, 1);

    /**
     * Computes `number` rounded down to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round down.
     * @param {number} [precision=0] The precision to round down to.
     * @returns {number} Returns the rounded down number.
     * @example
     *
     * _.floor(4.006);
     * // => 4
     *
     * _.floor(0.046, 2);
     * // => 0.04
     *
     * _.floor(4060, -2);
     * // => 4000
     */
    var floor = createRound('floor');

    /**
     * Computes the maximum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * _.max([4, 2, 8, 6]);
     * // => 8
     *
     * _.max([]);
     * // => undefined
     */
    function max(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseGt)
        : undefined;
    }

    /**
     * This method is like `_.max` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {*} Returns the maximum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.maxBy(objects, function(o) { return o.n; });
     * // => { 'n': 2 }
     *
     * // The `_.property` iteratee shorthand.
     * _.maxBy(objects, 'n');
     * // => { 'n': 2 }
     */
    function maxBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee, 2), baseGt)
        : undefined;
    }

    /**
     * Computes the mean of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the mean.
     * @example
     *
     * _.mean([4, 2, 8, 6]);
     * // => 5
     */
    function mean(array) {
      return baseMean(array, identity);
    }

    /**
     * This method is like `_.mean` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be averaged.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the mean.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.meanBy(objects, function(o) { return o.n; });
     * // => 5
     *
     * // The `_.property` iteratee shorthand.
     * _.meanBy(objects, 'n');
     * // => 5
     */
    function meanBy(array, iteratee) {
      return baseMean(array, getIteratee(iteratee, 2));
    }

    /**
     * Computes the minimum value of `array`. If `array` is empty or falsey,
     * `undefined` is returned.
     *
     * @static
     * @since 0.1.0
     * @memberOf _
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * _.min([4, 2, 8, 6]);
     * // => 2
     *
     * _.min([]);
     * // => undefined
     */
    function min(array) {
      return (array && array.length)
        ? baseExtremum(array, identity, baseLt)
        : undefined;
    }

    /**
     * This method is like `_.min` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the criterion by which
     * the value is ranked. The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {*} Returns the minimum value.
     * @example
     *
     * var objects = [{ 'n': 1 }, { 'n': 2 }];
     *
     * _.minBy(objects, function(o) { return o.n; });
     * // => { 'n': 1 }
     *
     * // The `_.property` iteratee shorthand.
     * _.minBy(objects, 'n');
     * // => { 'n': 1 }
     */
    function minBy(array, iteratee) {
      return (array && array.length)
        ? baseExtremum(array, getIteratee(iteratee, 2), baseLt)
        : undefined;
    }

    /**
     * Multiply two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.7.0
     * @category Math
     * @param {number} multiplier The first number in a multiplication.
     * @param {number} multiplicand The second number in a multiplication.
     * @returns {number} Returns the product.
     * @example
     *
     * _.multiply(6, 4);
     * // => 24
     */
    var multiply = createMathOperation(function(multiplier, multiplicand) {
      return multiplier * multiplicand;
    }, 1);

    /**
     * Computes `number` rounded to `precision`.
     *
     * @static
     * @memberOf _
     * @since 3.10.0
     * @category Math
     * @param {number} number The number to round.
     * @param {number} [precision=0] The precision to round to.
     * @returns {number} Returns the rounded number.
     * @example
     *
     * _.round(4.006);
     * // => 4
     *
     * _.round(4.006, 2);
     * // => 4.01
     *
     * _.round(4060, -2);
     * // => 4100
     */
    var round = createRound('round');

    /**
     * Subtract two numbers.
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {number} minuend The first number in a subtraction.
     * @param {number} subtrahend The second number in a subtraction.
     * @returns {number} Returns the difference.
     * @example
     *
     * _.subtract(6, 4);
     * // => 2
     */
    var subtract = createMathOperation(function(minuend, subtrahend) {
      return minuend - subtrahend;
    }, 0);

    /**
     * Computes the sum of the values in `array`.
     *
     * @static
     * @memberOf _
     * @since 3.4.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @returns {number} Returns the sum.
     * @example
     *
     * _.sum([4, 2, 8, 6]);
     * // => 20
     */
    function sum(array) {
      return (array && array.length)
        ? baseSum(array, identity)
        : 0;
    }

    /**
     * This method is like `_.sum` except that it accepts `iteratee` which is
     * invoked for each element in `array` to generate the value to be summed.
     * The iteratee is invoked with one argument: (value).
     *
     * @static
     * @memberOf _
     * @since 4.0.0
     * @category Math
     * @param {Array} array The array to iterate over.
     * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
     * @returns {number} Returns the sum.
     * @example
     *
     * var objects = [{ 'n': 4 }, { 'n': 2 }, { 'n': 8 }, { 'n': 6 }];
     *
     * _.sumBy(objects, function(o) { return o.n; });
     * // => 20
     *
     * // The `_.property` iteratee shorthand.
     * _.sumBy(objects, 'n');
     * // => 20
     */
    function sumBy(array, iteratee) {
      return (array && array.length)
        ? baseSum(array, getIteratee(iteratee, 2))
        : 0;
    }

    /*------------------------------------------------------------------------*/

    // Add methods that return wrapped values in chain sequences.
    lodash.after = after;
    lodash.ary = ary;
    lodash.assign = assign;
    lodash.assignIn = assignIn;
    lodash.assignInWith = assignInWith;
    lodash.assignWith = assignWith;
    lodash.at = at;
    lodash.before = before;
    lodash.bind = bind;
    lodash.bindAll = bindAll;
    lodash.bindKey = bindKey;
    lodash.castArray = castArray;
    lodash.chain = chain;
    lodash.chunk = chunk;
    lodash.compact = compact;
    lodash.concat = concat;
    lodash.cond = cond;
    lodash.conforms = conforms;
    lodash.constant = constant;
    lodash.countBy = countBy;
    lodash.create = create;
    lodash.curry = curry;
    lodash.curryRight = curryRight;
    lodash.debounce = debounce;
    lodash.defaults = defaults;
    lodash.defaultsDeep = defaultsDeep;
    lodash.defer = defer;
    lodash.delay = delay;
    lodash.difference = difference;
    lodash.differenceBy = differenceBy;
    lodash.differenceWith = differenceWith;
    lodash.drop = drop;
    lodash.dropRight = dropRight;
    lodash.dropRightWhile = dropRightWhile;
    lodash.dropWhile = dropWhile;
    lodash.fill = fill;
    lodash.filter = filter;
    lodash.flatMap = flatMap;
    lodash.flatMapDeep = flatMapDeep;
    lodash.flatMapDepth = flatMapDepth;
    lodash.flatten = flatten;
    lodash.flattenDeep = flattenDeep;
    lodash.flattenDepth = flattenDepth;
    lodash.flip = flip;
    lodash.flow = flow;
    lodash.flowRight = flowRight;
    lodash.fromPairs = fromPairs;
    lodash.functions = functions;
    lodash.functionsIn = functionsIn;
    lodash.groupBy = groupBy;
    lodash.initial = initial;
    lodash.intersection = intersection;
    lodash.intersectionBy = intersectionBy;
    lodash.intersectionWith = intersectionWith;
    lodash.invert = invert;
    lodash.invertBy = invertBy;
    lodash.invokeMap = invokeMap;
    lodash.iteratee = iteratee;
    lodash.keyBy = keyBy;
    lodash.keys = keys;
    lodash.keysIn = keysIn;
    lodash.map = map;
    lodash.mapKeys = mapKeys;
    lodash.mapValues = mapValues;
    lodash.matches = matches;
    lodash.matchesProperty = matchesProperty;
    lodash.memoize = memoize;
    lodash.merge = merge;
    lodash.mergeWith = mergeWith;
    lodash.method = method;
    lodash.methodOf = methodOf;
    lodash.mixin = mixin;
    lodash.negate = negate;
    lodash.nthArg = nthArg;
    lodash.omit = omit;
    lodash.omitBy = omitBy;
    lodash.once = once;
    lodash.orderBy = orderBy;
    lodash.over = over;
    lodash.overArgs = overArgs;
    lodash.overEvery = overEvery;
    lodash.overSome = overSome;
    lodash.partial = partial;
    lodash.partialRight = partialRight;
    lodash.partition = partition;
    lodash.pick = pick;
    lodash.pickBy = pickBy;
    lodash.property = property;
    lodash.propertyOf = propertyOf;
    lodash.pull = pull;
    lodash.pullAll = pullAll;
    lodash.pullAllBy = pullAllBy;
    lodash.pullAllWith = pullAllWith;
    lodash.pullAt = pullAt;
    lodash.range = range;
    lodash.rangeRight = rangeRight;
    lodash.rearg = rearg;
    lodash.reject = reject;
    lodash.remove = remove;
    lodash.rest = rest;
    lodash.reverse = reverse;
    lodash.sampleSize = sampleSize;
    lodash.set = set;
    lodash.setWith = setWith;
    lodash.shuffle = shuffle;
    lodash.slice = slice;
    lodash.sortBy = sortBy;
    lodash.sortedUniq = sortedUniq;
    lodash.sortedUniqBy = sortedUniqBy;
    lodash.split = split;
    lodash.spread = spread;
    lodash.tail = tail;
    lodash.take = take;
    lodash.takeRight = takeRight;
    lodash.takeRightWhile = takeRightWhile;
    lodash.takeWhile = takeWhile;
    lodash.tap = tap;
    lodash.throttle = throttle;
    lodash.thru = thru;
    lodash.toArray = toArray;
    lodash.toPairs = toPairs;
    lodash.toPairsIn = toPairsIn;
    lodash.toPath = toPath;
    lodash.toPlainObject = toPlainObject;
    lodash.transform = transform;
    lodash.unary = unary;
    lodash.union = union;
    lodash.unionBy = unionBy;
    lodash.unionWith = unionWith;
    lodash.uniq = uniq;
    lodash.uniqBy = uniqBy;
    lodash.uniqWith = uniqWith;
    lodash.unset = unset;
    lodash.unzip = unzip;
    lodash.unzipWith = unzipWith;
    lodash.update = update;
    lodash.updateWith = updateWith;
    lodash.values = values;
    lodash.valuesIn = valuesIn;
    lodash.without = without;
    lodash.words = words;
    lodash.wrap = wrap;
    lodash.xor = xor;
    lodash.xorBy = xorBy;
    lodash.xorWith = xorWith;
    lodash.zip = zip;
    lodash.zipObject = zipObject;
    lodash.zipObjectDeep = zipObjectDeep;
    lodash.zipWith = zipWith;

    // Add aliases.
    lodash.entries = toPairs;
    lodash.entriesIn = toPairsIn;
    lodash.extend = assignIn;
    lodash.extendWith = assignInWith;

    // Add methods to `lodash.prototype`.
    mixin(lodash, lodash);

    /*------------------------------------------------------------------------*/

    // Add methods that return unwrapped values in chain sequences.
    lodash.add = add;
    lodash.attempt = attempt;
    lodash.camelCase = camelCase;
    lodash.capitalize = capitalize;
    lodash.ceil = ceil;
    lodash.clamp = clamp;
    lodash.clone = clone;
    lodash.cloneDeep = cloneDeep;
    lodash.cloneDeepWith = cloneDeepWith;
    lodash.cloneWith = cloneWith;
    lodash.conformsTo = conformsTo;
    lodash.deburr = deburr;
    lodash.defaultTo = defaultTo;
    lodash.divide = divide;
    lodash.endsWith = endsWith;
    lodash.eq = eq;
    lodash.escape = escape;
    lodash.escapeRegExp = escapeRegExp;
    lodash.every = every;
    lodash.find = find;
    lodash.findIndex = findIndex;
    lodash.findKey = findKey;
    lodash.findLast = findLast;
    lodash.findLastIndex = findLastIndex;
    lodash.findLastKey = findLastKey;
    lodash.floor = floor;
    lodash.forEach = forEach;
    lodash.forEachRight = forEachRight;
    lodash.forIn = forIn;
    lodash.forInRight = forInRight;
    lodash.forOwn = forOwn;
    lodash.forOwnRight = forOwnRight;
    lodash.get = get;
    lodash.gt = gt;
    lodash.gte = gte;
    lodash.has = has;
    lodash.hasIn = hasIn;
    lodash.head = head;
    lodash.identity = identity;
    lodash.includes = includes;
    lodash.indexOf = indexOf;
    lodash.inRange = inRange;
    lodash.invoke = invoke;
    lodash.isArguments = isArguments;
    lodash.isArray = isArray;
    lodash.isArrayBuffer = isArrayBuffer;
    lodash.isArrayLike = isArrayLike;
    lodash.isArrayLikeObject = isArrayLikeObject;
    lodash.isBoolean = isBoolean;
    lodash.isBuffer = isBuffer;
    lodash.isDate = isDate;
    lodash.isElement = isElement;
    lodash.isEmpty = isEmpty;
    lodash.isEqual = isEqual;
    lodash.isEqualWith = isEqualWith;
    lodash.isError = isError;
    lodash.isFinite = isFinite;
    lodash.isFunction = isFunction;
    lodash.isInteger = isInteger;
    lodash.isLength = isLength;
    lodash.isMap = isMap;
    lodash.isMatch = isMatch;
    lodash.isMatchWith = isMatchWith;
    lodash.isNaN = isNaN;
    lodash.isNative = isNative;
    lodash.isNil = isNil;
    lodash.isNull = isNull;
    lodash.isNumber = isNumber;
    lodash.isObject = isObject;
    lodash.isObjectLike = isObjectLike;
    lodash.isPlainObject = isPlainObject;
    lodash.isRegExp = isRegExp;
    lodash.isSafeInteger = isSafeInteger;
    lodash.isSet = isSet;
    lodash.isString = isString;
    lodash.isSymbol = isSymbol;
    lodash.isTypedArray = isTypedArray;
    lodash.isUndefined = isUndefined;
    lodash.isWeakMap = isWeakMap;
    lodash.isWeakSet = isWeakSet;
    lodash.join = join;
    lodash.kebabCase = kebabCase;
    lodash.last = last;
    lodash.lastIndexOf = lastIndexOf;
    lodash.lowerCase = lowerCase;
    lodash.lowerFirst = lowerFirst;
    lodash.lt = lt;
    lodash.lte = lte;
    lodash.max = max;
    lodash.maxBy = maxBy;
    lodash.mean = mean;
    lodash.meanBy = meanBy;
    lodash.min = min;
    lodash.minBy = minBy;
    lodash.stubArray = stubArray;
    lodash.stubFalse = stubFalse;
    lodash.stubObject = stubObject;
    lodash.stubString = stubString;
    lodash.stubTrue = stubTrue;
    lodash.multiply = multiply;
    lodash.nth = nth;
    lodash.noConflict = noConflict;
    lodash.noop = noop;
    lodash.now = now;
    lodash.pad = pad;
    lodash.padEnd = padEnd;
    lodash.padStart = padStart;
    lodash.parseInt = parseInt;
    lodash.random = random;
    lodash.reduce = reduce;
    lodash.reduceRight = reduceRight;
    lodash.repeat = repeat;
    lodash.replace = replace;
    lodash.result = result;
    lodash.round = round;
    lodash.runInContext = runInContext;
    lodash.sample = sample;
    lodash.size = size;
    lodash.snakeCase = snakeCase;
    lodash.some = some;
    lodash.sortedIndex = sortedIndex;
    lodash.sortedIndexBy = sortedIndexBy;
    lodash.sortedIndexOf = sortedIndexOf;
    lodash.sortedLastIndex = sortedLastIndex;
    lodash.sortedLastIndexBy = sortedLastIndexBy;
    lodash.sortedLastIndexOf = sortedLastIndexOf;
    lodash.startCase = startCase;
    lodash.startsWith = startsWith;
    lodash.subtract = subtract;
    lodash.sum = sum;
    lodash.sumBy = sumBy;
    lodash.template = template;
    lodash.times = times;
    lodash.toFinite = toFinite;
    lodash.toInteger = toInteger;
    lodash.toLength = toLength;
    lodash.toLower = toLower;
    lodash.toNumber = toNumber;
    lodash.toSafeInteger = toSafeInteger;
    lodash.toString = toString;
    lodash.toUpper = toUpper;
    lodash.trim = trim;
    lodash.trimEnd = trimEnd;
    lodash.trimStart = trimStart;
    lodash.truncate = truncate;
    lodash.unescape = unescape;
    lodash.uniqueId = uniqueId;
    lodash.upperCase = upperCase;
    lodash.upperFirst = upperFirst;

    // Add aliases.
    lodash.each = forEach;
    lodash.eachRight = forEachRight;
    lodash.first = head;

    mixin(lodash, (function() {
      var source = {};
      baseForOwn(lodash, function(func, methodName) {
        if (!hasOwnProperty.call(lodash.prototype, methodName)) {
          source[methodName] = func;
        }
      });
      return source;
    }()), { 'chain': false });

    /*------------------------------------------------------------------------*/

    /**
     * The semantic version number.
     *
     * @static
     * @memberOf _
     * @type {string}
     */
    lodash.VERSION = VERSION;

    // Assign default placeholders.
    arrayEach(['bind', 'bindKey', 'curry', 'curryRight', 'partial', 'partialRight'], function(methodName) {
      lodash[methodName].placeholder = lodash;
    });

    // Add `LazyWrapper` methods for `_.drop` and `_.take` variants.
    arrayEach(['drop', 'take'], function(methodName, index) {
      LazyWrapper.prototype[methodName] = function(n) {
        n = n === undefined ? 1 : nativeMax(toInteger(n), 0);

        var result = (this.__filtered__ && !index)
          ? new LazyWrapper(this)
          : this.clone();

        if (result.__filtered__) {
          result.__takeCount__ = nativeMin(n, result.__takeCount__);
        } else {
          result.__views__.push({
            'size': nativeMin(n, MAX_ARRAY_LENGTH),
            'type': methodName + (result.__dir__ < 0 ? 'Right' : '')
          });
        }
        return result;
      };

      LazyWrapper.prototype[methodName + 'Right'] = function(n) {
        return this.reverse()[methodName](n).reverse();
      };
    });

    // Add `LazyWrapper` methods that accept an `iteratee` value.
    arrayEach(['filter', 'map', 'takeWhile'], function(methodName, index) {
      var type = index + 1,
          isFilter = type == LAZY_FILTER_FLAG || type == LAZY_WHILE_FLAG;

      LazyWrapper.prototype[methodName] = function(iteratee) {
        var result = this.clone();
        result.__iteratees__.push({
          'iteratee': getIteratee(iteratee, 3),
          'type': type
        });
        result.__filtered__ = result.__filtered__ || isFilter;
        return result;
      };
    });

    // Add `LazyWrapper` methods for `_.head` and `_.last`.
    arrayEach(['head', 'last'], function(methodName, index) {
      var takeName = 'take' + (index ? 'Right' : '');

      LazyWrapper.prototype[methodName] = function() {
        return this[takeName](1).value()[0];
      };
    });

    // Add `LazyWrapper` methods for `_.initial` and `_.tail`.
    arrayEach(['initial', 'tail'], function(methodName, index) {
      var dropName = 'drop' + (index ? '' : 'Right');

      LazyWrapper.prototype[methodName] = function() {
        return this.__filtered__ ? new LazyWrapper(this) : this[dropName](1);
      };
    });

    LazyWrapper.prototype.compact = function() {
      return this.filter(identity);
    };

    LazyWrapper.prototype.find = function(predicate) {
      return this.filter(predicate).head();
    };

    LazyWrapper.prototype.findLast = function(predicate) {
      return this.reverse().find(predicate);
    };

    LazyWrapper.prototype.invokeMap = baseRest(function(path, args) {
      if (typeof path == 'function') {
        return new LazyWrapper(this);
      }
      return this.map(function(value) {
        return baseInvoke(value, path, args);
      });
    });

    LazyWrapper.prototype.reject = function(predicate) {
      return this.filter(negate(getIteratee(predicate)));
    };

    LazyWrapper.prototype.slice = function(start, end) {
      start = toInteger(start);

      var result = this;
      if (result.__filtered__ && (start > 0 || end < 0)) {
        return new LazyWrapper(result);
      }
      if (start < 0) {
        result = result.takeRight(-start);
      } else if (start) {
        result = result.drop(start);
      }
      if (end !== undefined) {
        end = toInteger(end);
        result = end < 0 ? result.dropRight(-end) : result.take(end - start);
      }
      return result;
    };

    LazyWrapper.prototype.takeRightWhile = function(predicate) {
      return this.reverse().takeWhile(predicate).reverse();
    };

    LazyWrapper.prototype.toArray = function() {
      return this.take(MAX_ARRAY_LENGTH);
    };

    // Add `LazyWrapper` methods to `lodash.prototype`.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var checkIteratee = /^(?:filter|find|map|reject)|While$/.test(methodName),
          isTaker = /^(?:head|last)$/.test(methodName),
          lodashFunc = lodash[isTaker ? ('take' + (methodName == 'last' ? 'Right' : '')) : methodName],
          retUnwrapped = isTaker || /^find/.test(methodName);

      if (!lodashFunc) {
        return;
      }
      lodash.prototype[methodName] = function() {
        var value = this.__wrapped__,
            args = isTaker ? [1] : arguments,
            isLazy = value instanceof LazyWrapper,
            iteratee = args[0],
            useLazy = isLazy || isArray(value);

        var interceptor = function(value) {
          var result = lodashFunc.apply(lodash, arrayPush([value], args));
          return (isTaker && chainAll) ? result[0] : result;
        };

        if (useLazy && checkIteratee && typeof iteratee == 'function' && iteratee.length != 1) {
          // Avoid lazy use if the iteratee has a "length" value other than `1`.
          isLazy = useLazy = false;
        }
        var chainAll = this.__chain__,
            isHybrid = !!this.__actions__.length,
            isUnwrapped = retUnwrapped && !chainAll,
            onlyLazy = isLazy && !isHybrid;

        if (!retUnwrapped && useLazy) {
          value = onlyLazy ? value : new LazyWrapper(this);
          var result = func.apply(value, args);
          result.__actions__.push({ 'func': thru, 'args': [interceptor], 'thisArg': undefined });
          return new LodashWrapper(result, chainAll);
        }
        if (isUnwrapped && onlyLazy) {
          return func.apply(this, args);
        }
        result = this.thru(interceptor);
        return isUnwrapped ? (isTaker ? result.value()[0] : result.value()) : result;
      };
    });

    // Add `Array` methods to `lodash.prototype`.
    arrayEach(['pop', 'push', 'shift', 'sort', 'splice', 'unshift'], function(methodName) {
      var func = arrayProto[methodName],
          chainName = /^(?:push|sort|unshift)$/.test(methodName) ? 'tap' : 'thru',
          retUnwrapped = /^(?:pop|shift)$/.test(methodName);

      lodash.prototype[methodName] = function() {
        var args = arguments;
        if (retUnwrapped && !this.__chain__) {
          var value = this.value();
          return func.apply(isArray(value) ? value : [], args);
        }
        return this[chainName](function(value) {
          return func.apply(isArray(value) ? value : [], args);
        });
      };
    });

    // Map minified method names to their real names.
    baseForOwn(LazyWrapper.prototype, function(func, methodName) {
      var lodashFunc = lodash[methodName];
      if (lodashFunc) {
        var key = lodashFunc.name + '';
        if (!hasOwnProperty.call(realNames, key)) {
          realNames[key] = [];
        }
        realNames[key].push({ 'name': methodName, 'func': lodashFunc });
      }
    });

    realNames[createHybrid(undefined, WRAP_BIND_KEY_FLAG).name] = [{
      'name': 'wrapper',
      'func': undefined
    }];

    // Add methods to `LazyWrapper`.
    LazyWrapper.prototype.clone = lazyClone;
    LazyWrapper.prototype.reverse = lazyReverse;
    LazyWrapper.prototype.value = lazyValue;

    // Add chain sequence methods to the `lodash` wrapper.
    lodash.prototype.at = wrapperAt;
    lodash.prototype.chain = wrapperChain;
    lodash.prototype.commit = wrapperCommit;
    lodash.prototype.next = wrapperNext;
    lodash.prototype.plant = wrapperPlant;
    lodash.prototype.reverse = wrapperReverse;
    lodash.prototype.toJSON = lodash.prototype.valueOf = lodash.prototype.value = wrapperValue;

    // Add lazy aliases.
    lodash.prototype.first = lodash.prototype.head;

    if (symIterator) {
      lodash.prototype[symIterator] = wrapperToIterator;
    }
    return lodash;
  });

  /*--------------------------------------------------------------------------*/

  // Export lodash.
  var _ = runInContext();

  // Some AMD build optimizers, like r.js, check for condition patterns like:
  if (typeof define == 'function' && typeof define.amd == 'object' && define.amd) {
    // Expose Lodash on the global object to prevent errors when Lodash is
    // loaded by a script tag in the presence of an AMD loader.
    // See http://requirejs.org/docs/errors.html#mismatch for more details.
    // Use `_.noConflict` to remove Lodash from the global object.
    root._ = _;

    // Define as an anonymous module so, through path mapping, it can be
    // referenced as the "underscore" module.
    define(function() {
      return _;
    });
  }
  // Check for `exports` after `define` in case a build optimizer adds it.
  else if (freeModule) {
    // Export for Node.js.
    (freeModule.exports = _)._ = _;
    // Export for CommonJS support.
    freeExports._ = _;
  }
  else {
    // Export to the global object.
    root._ = _;
  }
}.call(this));

/**
 * An Angular module that gives you access to the browsers local storage
 * @version v0.2.6 - 2016-03-16
 * @link https://github.com/grevory/angular-local-storage
 * @author grevory <greg@gregpike.ca>
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */
(function (window, angular) {
var isDefined = angular.isDefined,
  isUndefined = angular.isUndefined,
  isNumber = angular.isNumber,
  isObject = angular.isObject,
  isArray = angular.isArray,
  extend = angular.extend,
  toJson = angular.toJson;

angular
  .module('LocalStorageModule', [])
  .provider('localStorageService', function() {
    // You should set a prefix to avoid overwriting any local storage variables from the rest of your app
    // e.g. localStorageServiceProvider.setPrefix('yourAppName');
    // With provider you can use config as this:
    // myApp.config(function (localStorageServiceProvider) {
    //    localStorageServiceProvider.prefix = 'yourAppName';
    // });
    this.prefix = 'ls';

    // You could change web storage type localstorage or sessionStorage
    this.storageType = 'localStorage';

    // Cookie options (usually in case of fallback)
    // expiry = Number of days before cookies expire // 0 = Does not expire
    // path = The web path the cookie represents
    this.cookie = {
      expiry: 30,
      path: '/'
    };

    // Send signals for each of the following actions?
    this.notify = {
      setItem: true,
      removeItem: false
    };

    // Setter for the prefix
    this.setPrefix = function(prefix) {
      this.prefix = prefix;
      return this;
    };

    // Setter for the storageType
    this.setStorageType = function(storageType) {
      this.storageType = storageType;
      return this;
    };

    // Setter for cookie config
    this.setStorageCookie = function(exp, path) {
      this.cookie.expiry = exp;
      this.cookie.path = path;
      return this;
    };

    // Setter for cookie domain
    this.setStorageCookieDomain = function(domain) {
      this.cookie.domain = domain;
      return this;
    };

    // Setter for notification config
    // itemSet & itemRemove should be booleans
    this.setNotify = function(itemSet, itemRemove) {
      this.notify = {
        setItem: itemSet,
        removeItem: itemRemove
      };
      return this;
    };

    this.$get = ['$rootScope', '$window', '$document', '$parse', function($rootScope, $window, $document, $parse) {
      var self = this;
      var prefix = self.prefix;
      var cookie = self.cookie;
      var notify = self.notify;
      var storageType = self.storageType;
      var webStorage;

      // When Angular's $document is not available
      if (!$document) {
        $document = document;
      } else if ($document[0]) {
        $document = $document[0];
      }

      // If there is a prefix set in the config lets use that with an appended period for readability
      if (prefix.substr(-1) !== '.') {
        prefix = !!prefix ? prefix + '.' : '';
      }
      var deriveQualifiedKey = function(key) {
        return prefix + key;
      };
      // Checks the browser to see if local storage is supported
      var browserSupportsLocalStorage = (function () {
        try {
          var supported = (storageType in $window && $window[storageType] !== null);

          // When Safari (OS X or iOS) is in private browsing mode, it appears as though localStorage
          // is available, but trying to call .setItem throws an exception.
          //
          // "QUOTA_EXCEEDED_ERR: DOM Exception 22: An attempt was made to add something to storage
          // that exceeded the quota."
          var key = deriveQualifiedKey('__' + Math.round(Math.random() * 1e7));
          if (supported) {
            webStorage = $window[storageType];
            webStorage.setItem(key, '');
            webStorage.removeItem(key);
          }

          return supported;
        } catch (e) {
          storageType = 'cookie';
          $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
          return false;
        }
      }());

      // Directly adds a value to local storage
      // If local storage is not available in the browser use cookies
      // Example use: localStorageService.add('library','angular');
      var addToLocalStorage = function (key, value) {
        // Let's convert undefined values to null to get the value consistent
        if (isUndefined(value)) {
          value = null;
        } else {
          value = toJson(value);
        }

        // If this browser does not support local storage use cookies
        if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
          if (!browserSupportsLocalStorage) {
            $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
          }

          if (notify.setItem) {
            $rootScope.$broadcast('LocalStorageModule.notification.setitem', {key: key, newvalue: value, storageType: 'cookie'});
          }
          return addToCookies(key, value);
        }

        try {
          if (webStorage) {
            webStorage.setItem(deriveQualifiedKey(key), value);
          }
          if (notify.setItem) {
            $rootScope.$broadcast('LocalStorageModule.notification.setitem', {key: key, newvalue: value, storageType: self.storageType});
          }
        } catch (e) {
          $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
          return addToCookies(key, value);
        }
        return true;
      };

      // Directly get a value from local storage
      // Example use: localStorageService.get('library'); // returns 'angular'
      var getFromLocalStorage = function (key) {

        if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
          if (!browserSupportsLocalStorage) {
            $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
          }

          return getFromCookies(key);
        }

        var item = webStorage ? webStorage.getItem(deriveQualifiedKey(key)) : null;
        // angular.toJson will convert null to 'null', so a proper conversion is needed
        // FIXME not a perfect solution, since a valid 'null' string can't be stored
        if (!item || item === 'null') {
          return null;
        }

        try {
          return JSON.parse(item);
        } catch (e) {
          return item;
        }
      };

      // Remove an item from local storage
      // Example use: localStorageService.remove('library'); // removes the key/value pair of library='angular'
      var removeFromLocalStorage = function () {
        var i, key;
        for (i=0; i<arguments.length; i++) {
          key = arguments[i];
          if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
            if (!browserSupportsLocalStorage) {
              $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
            }

            if (notify.removeItem) {
              $rootScope.$broadcast('LocalStorageModule.notification.removeitem', {key: key, storageType: 'cookie'});
            }
            removeFromCookies(key);
          }
          else {
            try {
              webStorage.removeItem(deriveQualifiedKey(key));
              if (notify.removeItem) {
                $rootScope.$broadcast('LocalStorageModule.notification.removeitem', {
                  key: key,
                  storageType: self.storageType
                });
              }
            } catch (e) {
              $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
              removeFromCookies(key);
            }
          }
        }
      };

      // Return array of keys for local storage
      // Example use: var keys = localStorageService.keys()
      var getKeysForLocalStorage = function () {

        if (!browserSupportsLocalStorage) {
          $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
          return [];
        }

        var prefixLength = prefix.length;
        var keys = [];
        for (var key in webStorage) {
          // Only return keys that are for this app
          if (key.substr(0, prefixLength) === prefix) {
            try {
              keys.push(key.substr(prefixLength));
            } catch (e) {
              $rootScope.$broadcast('LocalStorageModule.notification.error', e.Description);
              return [];
            }
          }
        }
        return keys;
      };

      // Remove all data for this app from local storage
      // Also optionally takes a regular expression string and removes the matching key-value pairs
      // Example use: localStorageService.clearAll();
      // Should be used mostly for development purposes
      var clearAllFromLocalStorage = function (regularExpression) {

        // Setting both regular expressions independently
        // Empty strings result in catchall RegExp
        var prefixRegex = !!prefix ? new RegExp('^' + prefix) : new RegExp();
        var testRegex = !!regularExpression ? new RegExp(regularExpression) : new RegExp();

        if (!browserSupportsLocalStorage || self.storageType === 'cookie') {
          if (!browserSupportsLocalStorage) {
            $rootScope.$broadcast('LocalStorageModule.notification.warning', 'LOCAL_STORAGE_NOT_SUPPORTED');
          }
          return clearAllFromCookies();
        }

        var prefixLength = prefix.length;

        for (var key in webStorage) {
          // Only remove items that are for this app and match the regular expression
          if (prefixRegex.test(key) && testRegex.test(key.substr(prefixLength))) {
            try {
              removeFromLocalStorage(key.substr(prefixLength));
            } catch (e) {
              $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
              return clearAllFromCookies();
            }
          }
        }
        return true;
      };

      // Checks the browser to see if cookies are supported
      var browserSupportsCookies = (function() {
        try {
          return $window.navigator.cookieEnabled ||
          ("cookie" in $document && ($document.cookie.length > 0 ||
            ($document.cookie = "test").indexOf.call($document.cookie, "test") > -1));
          } catch (e) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
            return false;
          }
        }());

        // Directly adds a value to cookies
        // Typically used as a fallback is local storage is not available in the browser
        // Example use: localStorageService.cookie.add('library','angular');
        var addToCookies = function (key, value, daysToExpiry) {

          if (isUndefined(value)) {
            return false;
          } else if(isArray(value) || isObject(value)) {
            value = toJson(value);
          }

          if (!browserSupportsCookies) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', 'COOKIES_NOT_SUPPORTED');
            return false;
          }

          try {
            var expiry = '',
            expiryDate = new Date(),
            cookieDomain = '';

            if (value === null) {
              // Mark that the cookie has expired one day ago
              expiryDate.setTime(expiryDate.getTime() + (-1 * 24 * 60 * 60 * 1000));
              expiry = "; expires=" + expiryDate.toGMTString();
              value = '';
            } else if (isNumber(daysToExpiry) && daysToExpiry !== 0) {
              expiryDate.setTime(expiryDate.getTime() + (daysToExpiry * 24 * 60 * 60 * 1000));
              expiry = "; expires=" + expiryDate.toGMTString();
            } else if (cookie.expiry !== 0) {
              expiryDate.setTime(expiryDate.getTime() + (cookie.expiry * 24 * 60 * 60 * 1000));
              expiry = "; expires=" + expiryDate.toGMTString();
            }
            if (!!key) {
              var cookiePath = "; path=" + cookie.path;
              if(cookie.domain){
                cookieDomain = "; domain=" + cookie.domain;
              }
              $document.cookie = deriveQualifiedKey(key) + "=" + encodeURIComponent(value) + expiry + cookiePath + cookieDomain;
            }
          } catch (e) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', e.message);
            return false;
          }
          return true;
        };

        // Directly get a value from a cookie
        // Example use: localStorageService.cookie.get('library'); // returns 'angular'
        var getFromCookies = function (key) {
          if (!browserSupportsCookies) {
            $rootScope.$broadcast('LocalStorageModule.notification.error', 'COOKIES_NOT_SUPPORTED');
            return false;
          }

          var cookies = $document.cookie && $document.cookie.split(';') || [];
          for(var i=0; i < cookies.length; i++) {
            var thisCookie = cookies[i];
            while (thisCookie.charAt(0) === ' ') {
              thisCookie = thisCookie.substring(1,thisCookie.length);
            }
            if (thisCookie.indexOf(deriveQualifiedKey(key) + '=') === 0) {
              var storedValues = decodeURIComponent(thisCookie.substring(prefix.length + key.length + 1, thisCookie.length));
              try {
                return JSON.parse(storedValues);
              } catch(e) {
                return storedValues;
              }
            }
          }
          return null;
        };

        var removeFromCookies = function (key) {
          addToCookies(key,null);
        };

        var clearAllFromCookies = function () {
          var thisCookie = null, thisKey = null;
          var prefixLength = prefix.length;
          var cookies = $document.cookie.split(';');
          for(var i = 0; i < cookies.length; i++) {
            thisCookie = cookies[i];

            while (thisCookie.charAt(0) === ' ') {
              thisCookie = thisCookie.substring(1, thisCookie.length);
            }

            var key = thisCookie.substring(prefixLength, thisCookie.indexOf('='));
            removeFromCookies(key);
          }
        };

        var getStorageType = function() {
          return storageType;
        };

        // Add a listener on scope variable to save its changes to local storage
        // Return a function which when called cancels binding
        var bindToScope = function(scope, key, def, lsKey) {
          lsKey = lsKey || key;
          var value = getFromLocalStorage(lsKey);

          if (value === null && isDefined(def)) {
            value = def;
          } else if (isObject(value) && isObject(def)) {
            value = extend(value, def);
          }

          $parse(key).assign(scope, value);

          return scope.$watch(key, function(newVal) {
            addToLocalStorage(lsKey, newVal);
          }, isObject(scope[key]));
        };

        // Return localStorageService.length
        // ignore keys that not owned
        var lengthOfLocalStorage = function() {
          var count = 0;
          var storage = $window[storageType];
          for(var i = 0; i < storage.length; i++) {
            if(storage.key(i).indexOf(prefix) === 0 ) {
              count++;
            }
          }
          return count;
        };

        return {
          isSupported: browserSupportsLocalStorage,
          getStorageType: getStorageType,
          set: addToLocalStorage,
          add: addToLocalStorage, //DEPRECATED
          get: getFromLocalStorage,
          keys: getKeysForLocalStorage,
          remove: removeFromLocalStorage,
          clearAll: clearAllFromLocalStorage,
          bind: bindToScope,
          deriveKey: deriveQualifiedKey,
          length: lengthOfLocalStorage,
          cookie: {
            isSupported: browserSupportsCookies,
            set: addToCookies,
            add: addToCookies, //DEPRECATED
            get: getFromCookies,
            remove: removeFromCookies,
            clearAll: clearAllFromCookies
          }
        };
      }];
  });
})(window, window.angular);
/**
 * Restful Resources service for AngularJS apps
 * @version v1.6.1 - 2017-01-06 * @link https://github.com/mgonto/restangular
 * @author Martin Gontovnikas <martin@gon.to>
 * @license MIT License, http://www.opensource.org/licenses/MIT
 */(function(root, factory) {
  /* global define, require */
  // https://github.com/umdjs/umd/blob/master/templates/returnExports.js
  if (typeof define === 'function' && define.amd) {
    define(['lodash', 'angular'], factory);
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory(require('lodash'), require('angular'));
  } else {
    // No global export, Restangular will register itself as Angular.js module
    factory(root._, root.angular);
  }
}(this, function(_, angular) {

  var restangular = angular.module('restangular', []);

  restangular.provider('Restangular', function() {
    // Configuration
    var Configurer = {};
    Configurer.init = function(object, config) {
      object.configuration = config;

      /**
       * Those are HTTP safe methods for which there is no need to pass any data with the request.
       */
      var safeMethods = ['get', 'head', 'options', 'trace', 'getlist'];
      config.isSafe = function(operation) {
        return _.includes(safeMethods, operation.toLowerCase());
      };

      var absolutePattern = /^https?:\/\//i;
      config.isAbsoluteUrl = function(string) {
        return _.isUndefined(config.absoluteUrl) || _.isNull(config.absoluteUrl) ?
          string && absolutePattern.test(string) :
          config.absoluteUrl;
      };

      config.absoluteUrl = _.isUndefined(config.absoluteUrl) ? true : config.absoluteUrl;
      object.setSelfLinkAbsoluteUrl = function(value) {
        config.absoluteUrl = value;
      };
      /**
       * This is the BaseURL to be used with Restangular
       */
      config.baseUrl = _.isUndefined(config.baseUrl) ? '' : config.baseUrl;
      object.setBaseUrl = function(newBaseUrl) {
        config.baseUrl = /\/$/.test(newBaseUrl) ?
          newBaseUrl.substring(0, newBaseUrl.length - 1) :
          newBaseUrl;
        return this;
      };

      /**
       * Sets the extra fields to keep from the parents
       */
      config.extraFields = config.extraFields || [];
      object.setExtraFields = function(newExtraFields) {
        config.extraFields = newExtraFields;
        return this;
      };

      /**
       * Some default $http parameter to be used in EVERY call
       **/
      config.defaultHttpFields = config.defaultHttpFields || {};
      object.setDefaultHttpFields = function(values) {
        config.defaultHttpFields = values;
        return this;
      };

      /**
       * Always return plain data, no restangularized object
       **/
      config.plainByDefault = config.plainByDefault || false;
      object.setPlainByDefault = function(value) {
        config.plainByDefault = value === true ? true : false;
        return this;
      };

      config.withHttpValues = function(httpLocalConfig, obj) {
        return _.defaults(obj, httpLocalConfig, config.defaultHttpFields);
      };

      config.encodeIds = _.isUndefined(config.encodeIds) ? true : config.encodeIds;
      object.setEncodeIds = function(encode) {
        config.encodeIds = encode;
      };

      config.defaultRequestParams = config.defaultRequestParams || {
        get: {},
        post: {},
        put: {},
        remove: {},
        common: {}
      };

      object.setDefaultRequestParams = function(param1, param2) {
        var methods = [],
          params = param2 || param1;
        if (!_.isUndefined(param2)) {
          if (_.isArray(param1)) {
            methods = param1;
          } else {
            methods.push(param1);
          }
        } else {
          methods.push('common');
        }

        _.each(methods, function(method) {
          config.defaultRequestParams[method] = params;
        });
        return this;
      };

      object.requestParams = config.defaultRequestParams;

      config.defaultHeaders = config.defaultHeaders || {};
      object.setDefaultHeaders = function(headers) {
        config.defaultHeaders = headers;
        object.defaultHeaders = config.defaultHeaders;
        return this;
      };

      object.defaultHeaders = config.defaultHeaders;

      /**
       * Method overriders will set which methods are sent via POST with an X-HTTP-Method-Override
       **/
      config.methodOverriders = config.methodOverriders || [];
      object.setMethodOverriders = function(values) {
        var overriders = _.extend([], values);
        if (config.isOverridenMethod('delete', overriders)) {
          overriders.push('remove');
        }
        config.methodOverriders = overriders;
        return this;
      };

      config.jsonp = _.isUndefined(config.jsonp) ? false : config.jsonp;
      object.setJsonp = function(active) {
        config.jsonp = active;
      };

      config.isOverridenMethod = function(method, values) {
        var search = values || config.methodOverriders;
        return !_.isUndefined(_.find(search, function(one) {
          return one.toLowerCase() === method.toLowerCase();
        }));
      };

      /**
       * Sets the URL creator type. For now, only Path is created. In the future we'll have queryParams
       **/
      config.urlCreator = config.urlCreator || 'path';
      object.setUrlCreator = function(name) {
        if (!_.has(config.urlCreatorFactory, name)) {
          throw new Error('URL Path selected isn\'t valid');
        }

        config.urlCreator = name;
        return this;
      };

      /**
       * You can set the restangular fields here. The 3 required fields for Restangular are:
       *
       * id: Id of the element
       * route: name of the route of this element
       * parentResource: the reference to the parent resource
       *
       *  All of this fields except for id, are handled (and created) by Restangular. By default,
       *  the field values will be id, route and parentResource respectively
       */
      config.restangularFields = config.restangularFields || {
        id: 'id',
        route: 'route',
        parentResource: 'parentResource',
        restangularCollection: 'restangularCollection',
        cannonicalId: '__cannonicalId',
        etag: 'restangularEtag',
        selfLink: 'href',
        get: 'get',
        getList: 'getList',
        put: 'put',
        post: 'post',
        remove: 'remove',
        head: 'head',
        trace: 'trace',
        options: 'options',
        patch: 'patch',
        getRestangularUrl: 'getRestangularUrl',
        getRequestedUrl: 'getRequestedUrl',
        putElement: 'putElement',
        addRestangularMethod: 'addRestangularMethod',
        getParentList: 'getParentList',
        clone: 'clone',
        ids: 'ids',
        httpConfig: '_$httpConfig',
        reqParams: 'reqParams',
        one: 'one',
        all: 'all',
        several: 'several',
        oneUrl: 'oneUrl',
        allUrl: 'allUrl',
        customPUT: 'customPUT',
        customPATCH: 'customPATCH',
        customPOST: 'customPOST',
        customDELETE: 'customDELETE',
        customGET: 'customGET',
        customGETLIST: 'customGETLIST',
        customOperation: 'customOperation',
        doPUT: 'doPUT',
        doPATCH: 'doPATCH',
        doPOST: 'doPOST',
        doDELETE: 'doDELETE',
        doGET: 'doGET',
        doGETLIST: 'doGETLIST',
        fromServer: 'fromServer',
        withConfig: 'withConfig',
        withHttpConfig: 'withHttpConfig',
        singleOne: 'singleOne',
        plain: 'plain',
        save: 'save',
        restangularized: 'restangularized'
      };
      object.setRestangularFields = function(resFields) {
        config.restangularFields =
          _.extend(config.restangularFields, resFields);
        return this;
      };

      config.isRestangularized = function(obj) {
        return !!obj[config.restangularFields.restangularized];
      };

      config.setFieldToElem = function(field, elem, value) {
        var properties = field.split('.');
        var idValue = elem;
        _.each(_.initial(properties), function(prop) {
          idValue[prop] = {};
          idValue = idValue[prop];
        });
        idValue[_.last(properties)] = value;
        return this;
      };

      config.getFieldFromElem = function(field, elem) {
        var properties = field.split('.');
        var idValue = elem;
        _.each(properties, function(prop) {
          if (idValue) {
            idValue = idValue[prop];
          }
        });
        return angular.copy(idValue);
      };

      config.setIdToElem = function(elem, id /*, route */ ) {
        config.setFieldToElem(config.restangularFields.id, elem, id);
        return this;
      };

      config.getIdFromElem = function(elem) {
        return config.getFieldFromElem(config.restangularFields.id, elem);
      };

      config.isValidId = function(elemId) {
        return '' !== elemId && !_.isUndefined(elemId) && !_.isNull(elemId);
      };

      config.setUrlToElem = function(elem, url /*, route */ ) {
        config.setFieldToElem(config.restangularFields.selfLink, elem, url);
        return this;
      };

      config.getUrlFromElem = function(elem) {
        return config.getFieldFromElem(config.restangularFields.selfLink, elem);
      };

      config.useCannonicalId = _.isUndefined(config.useCannonicalId) ? false : config.useCannonicalId;
      object.setUseCannonicalId = function(value) {
        config.useCannonicalId = value;
        return this;
      };

      config.getCannonicalIdFromElem = function(elem) {
        var cannonicalId = elem[config.restangularFields.cannonicalId];
        var actualId = config.isValidId(cannonicalId) ? cannonicalId : config.getIdFromElem(elem);
        return actualId;
      };

      /**
       * Sets the Response parser. This is used in case your response isn't directly the data.
       * For example if you have a response like {meta: {'meta'}, data: {name: 'Gonto'}}
       * you can extract this data which is the one that needs wrapping
       *
       * The ResponseExtractor is a function that receives the response and the method executed.
       */

      config.responseInterceptors = config.responseInterceptors || [];

      config.defaultResponseInterceptor = function(data /*, operation, what, url, response, deferred */ ) {
        return data;
      };

      config.responseExtractor = function(data, operation, what, url, response, deferred) {
        var interceptors = angular.copy(config.responseInterceptors);
        interceptors.push(config.defaultResponseInterceptor);
        var theData = data;
        _.each(interceptors, function(interceptor) {
          theData = interceptor(theData, operation,
            what, url, response, deferred);
        });
        return theData;
      };

      object.addResponseInterceptor = function(extractor) {
        config.responseInterceptors.push(extractor);
        return this;
      };

      config.errorInterceptors = config.errorInterceptors || [];
      object.addErrorInterceptor = function(interceptor) {
        config.errorInterceptors.push(interceptor);
        return this;
      };

      object.setResponseInterceptor = object.addResponseInterceptor;
      object.setResponseExtractor = object.addResponseInterceptor;
      object.setErrorInterceptor = object.addErrorInterceptor;

      /**
       * Response interceptor is called just before resolving promises.
       */


      /**
       * Request interceptor is called before sending an object to the server.
       */
      config.requestInterceptors = config.requestInterceptors || [];

      config.defaultInterceptor = function(element, operation, path, url, headers, params, httpConfig) {
        return {
          element: element,
          headers: headers,
          params: params,
          httpConfig: httpConfig
        };
      };

      config.fullRequestInterceptor = function(element, operation, path, url, headers, params, httpConfig) {
        var interceptors = angular.copy(config.requestInterceptors);
        var defaultRequest = config.defaultInterceptor(element, operation, path, url, headers, params, httpConfig);
        return _.reduce(interceptors, function(request, interceptor) {
          return _.extend(request, interceptor(request.element, operation,
            path, url, request.headers, request.params, request.httpConfig));
        }, defaultRequest);
      };

      object.addRequestInterceptor = function(interceptor) {
        config.requestInterceptors.push(function(elem, operation, path, url, headers, params, httpConfig) {
          return {
            headers: headers,
            params: params,
            element: interceptor(elem, operation, path, url),
            httpConfig: httpConfig
          };
        });
        return this;
      };

      object.setRequestInterceptor = object.addRequestInterceptor;

      object.addFullRequestInterceptor = function(interceptor) {
        config.requestInterceptors.push(interceptor);
        return this;
      };

      object.setFullRequestInterceptor = object.addFullRequestInterceptor;

      config.onBeforeElemRestangularized = config.onBeforeElemRestangularized || function(elem) {
        return elem;
      };
      object.setOnBeforeElemRestangularized = function(post) {
        config.onBeforeElemRestangularized = post;
        return this;
      };

      object.setRestangularizePromiseInterceptor = function(interceptor) {
        config.restangularizePromiseInterceptor = interceptor;
        return this;
      };

      /**
       * This method is called after an element has been "Restangularized".
       *
       * It receives the element, a boolean indicating if it's an element or a collection
       * and the name of the model
       *
       */
      config.onElemRestangularized = config.onElemRestangularized || function(elem) {
        return elem;
      };
      object.setOnElemRestangularized = function(post) {
        config.onElemRestangularized = post;
        return this;
      };

      config.shouldSaveParent = config.shouldSaveParent || function() {
        return true;
      };
      object.setParentless = function(values) {
        if (_.isArray(values)) {
          config.shouldSaveParent = function(route) {
            return !_.includes(values, route);
          };
        } else if (_.isBoolean(values)) {
          config.shouldSaveParent = function() {
            return !values;
          };
        }
        return this;
      };

      /**
       * This lets you set a suffix to every request.
       *
       * For example, if your api requires that for JSon requests you do /users/123.json, you can set that
       * in here.
       *
       *
       * By default, the suffix is null
       */
      config.suffix = _.isUndefined(config.suffix) ? null : config.suffix;
      object.setRequestSuffix = function(newSuffix) {
        config.suffix = newSuffix;
        return this;
      };

      /**
       * Add element transformers for certain routes.
       */
      config.transformers = config.transformers || {};
      config.matchTransformers = config.matchTransformers || [];
      object.addElementTransformer = function(type, secondArg, thirdArg) {
        var isCollection = null;
        var transformer = null;
        if (arguments.length === 2) {
          transformer = secondArg;
        } else {
          transformer = thirdArg;
          isCollection = secondArg;
        }

        var transformerFn = function(coll, elem) {
          if (_.isNull(isCollection) || (coll === isCollection)) {
            return transformer(elem);
          }
          return elem;
        };

        if (_.isRegExp(type)) {
          config.matchTransformers.push({
            regexp: type,
            transformer: transformerFn
          });
        } else {
          if (!config.transformers[type]) {
            config.transformers[type] = [];
          }
          config.transformers[type].push(transformerFn);
        }

        return object;
      };

      object.extendCollection = function(route, fn) {
        return object.addElementTransformer(route, true, fn);
      };

      object.extendModel = function(route, fn) {
        return object.addElementTransformer(route, false, fn);
      };

      config.transformElem = function(elem, isCollection, route, Restangular, force) {
        if (!force && !config.transformLocalElements && !elem[config.restangularFields.fromServer]) {
          return elem;
        }

        var changedElem = elem;

        var matchTransformers = config.matchTransformers;
        if (matchTransformers) {
          _.each(matchTransformers, function(transformer) {
            if (transformer.regexp.test(route)) {
              changedElem = transformer.transformer(isCollection, changedElem);
            }
          });
        }

        var typeTransformers = config.transformers[route];
        if (typeTransformers) {
          _.each(typeTransformers, function(transformer) {
            changedElem = transformer(isCollection, changedElem);
          });
        }
        return config.onElemRestangularized(changedElem, isCollection, route, Restangular);
      };

      config.transformLocalElements = _.isUndefined(config.transformLocalElements) ?
        false :
        config.transformLocalElements;

      object.setTransformOnlyServerElements = function(active) {
        config.transformLocalElements = !active;
      };

      config.fullResponse = _.isUndefined(config.fullResponse) ? false : config.fullResponse;
      object.setFullResponse = function(full) {
        config.fullResponse = full;
        return this;
      };


      //Internal values and functions
      config.urlCreatorFactory = {};

      /**
       * Base URL Creator. Base prototype for everything related to it
       **/

      var BaseCreator = function() {};

      BaseCreator.prototype.setConfig = function(config) {
        this.config = config;
        return this;
      };

      BaseCreator.prototype.parentsArray = function(current) {
        var parents = [];
        while (current) {
          parents.push(current);
          current = current[this.config.restangularFields.parentResource];
        }
        return parents.reverse();
      };

      function RestangularResource(config, $http, url, configurer) {
        var resource = {};
        _.each(_.keys(configurer), function(key) {
          var value = configurer[key];

          // Add default parameters
          value.params = _.extend({}, value.params, config.defaultRequestParams[value.method.toLowerCase()]);
          // We don't want the ? if no params are there
          if (_.isEmpty(value.params)) {
            delete value.params;
          }

          if (config.isSafe(value.method)) {

            resource[key] = function() {
              return $http(_.extend(value, {
                url: url
              }));
            };

          } else {

            resource[key] = function(data) {
              return $http(_.extend(value, {
                url: url,
                data: data
              }));
            };

          }
        });

        return resource;
      }

      BaseCreator.prototype.resource = function(current, $http, localHttpConfig, callHeaders, callParams, what, etag, operation) {

        var params = _.defaults(callParams || {}, this.config.defaultRequestParams.common);
        var headers = _.defaults(callHeaders || {}, this.config.defaultHeaders);

        if (etag) {
          if (!config.isSafe(operation)) {
            headers['If-Match'] = etag;
          } else {
            headers['If-None-Match'] = etag;
          }
        }

        var url = this.base(current);

        if (what || what === 0) {
          var add = '';
          if (!/\/$/.test(url)) {
            add += '/';
          }
          add += what;
          url += add;
        }

        if (this.config.suffix &&
          url.indexOf(this.config.suffix, url.length - this.config.suffix.length) === -1 &&
          !this.config.getUrlFromElem(current)) {
          url += this.config.suffix;
        }

        current[this.config.restangularFields.httpConfig] = undefined;

        return RestangularResource(this.config, $http, url, {
          getList: this.config.withHttpValues(localHttpConfig, {
            method: 'GET',
            params: params,
            headers: headers
          }),

          get: this.config.withHttpValues(localHttpConfig, {
            method: 'GET',
            params: params,
            headers: headers
          }),

          jsonp: this.config.withHttpValues(localHttpConfig, {
            method: 'jsonp',
            params: params,
            headers: headers
          }),

          put: this.config.withHttpValues(localHttpConfig, {
            method: 'PUT',
            params: params,
            headers: headers
          }),

          post: this.config.withHttpValues(localHttpConfig, {
            method: 'POST',
            params: params,
            headers: headers
          }),

          remove: this.config.withHttpValues(localHttpConfig, {
            method: 'DELETE',
            params: params,
            headers: headers
          }),

          head: this.config.withHttpValues(localHttpConfig, {
            method: 'HEAD',
            params: params,
            headers: headers
          }),

          trace: this.config.withHttpValues(localHttpConfig, {
            method: 'TRACE',
            params: params,
            headers: headers
          }),

          options: this.config.withHttpValues(localHttpConfig, {
            method: 'OPTIONS',
            params: params,
            headers: headers
          }),

          patch: this.config.withHttpValues(localHttpConfig, {
            method: 'PATCH',
            params: params,
            headers: headers
          })
        });
      };

      /**
       * This is the Path URL creator. It uses Path to show Hierarchy in the Rest API.
       * This means that if you have an Account that then has a set of Buildings, a URL to a building
       * would be /accounts/123/buildings/456
       **/
      var Path = function() {};

      Path.prototype = new BaseCreator();

      Path.prototype.normalizeUrl = function(url) {
        var parts = /((?:http[s]?:)?\/\/)?(.*)?/.exec(url);
        parts[2] = parts[2].replace(/[\\\/]+/g, '/');
        return (typeof parts[1] !== 'undefined') ? parts[1] + parts[2] : parts[2];
      };

      Path.prototype.base = function(current) {
        var __this = this;
        return _.reduce(this.parentsArray(current), function(acum, elem) {
          var elemUrl;
          var elemSelfLink = __this.config.getUrlFromElem(elem);
          if (elemSelfLink) {
            if (__this.config.isAbsoluteUrl(elemSelfLink)) {
              return elemSelfLink;
            } else {
              elemUrl = elemSelfLink;
            }
          } else {
            elemUrl = elem[__this.config.restangularFields.route];

            if (elem[__this.config.restangularFields.restangularCollection]) {
              var ids = elem[__this.config.restangularFields.ids];
              if (ids) {
                elemUrl += '/' + ids.join(',');
              }
            } else {
              var elemId;
              if (__this.config.useCannonicalId) {
                elemId = __this.config.getCannonicalIdFromElem(elem);
              } else {
                elemId = __this.config.getIdFromElem(elem);
              }

              if (config.isValidId(elemId) && !elem.singleOne) {
                elemUrl += '/' + (__this.config.encodeIds ? encodeURIComponent(elemId) : elemId);
              }
            }
          }
          acum = acum.replace(/\/$/, '') + '/' + elemUrl;
          return __this.normalizeUrl(acum);

        }, this.config.baseUrl);
      };



      Path.prototype.fetchUrl = function(current, what) {
        var baseUrl = this.base(current);
        if (what) {
          baseUrl += '/' + what;
        }
        return baseUrl;
      };

      Path.prototype.fetchRequestedUrl = function(current, what) {
        var url = this.fetchUrl(current, what);
        var params = current[config.restangularFields.reqParams];

        // From here on and until the end of fetchRequestedUrl,
        // the code has been kindly borrowed from angular.js
        // The reason for such code bloating is coherence:
        //   If the user were to use this for cache management, the
        //   serialization of parameters would need to be identical
        //   to the one done by angular for cache keys to match.
        function sortedKeys(obj) {
          var keys = [];
          for (var key in obj) {
            if (obj.hasOwnProperty(key)) {
              keys.push(key);
            }
          }
          return keys.sort();
        }

        function forEachSorted(obj, iterator, context) {
          var keys = sortedKeys(obj);
          for (var i = 0; i < keys.length; i++) {
            iterator.call(context, obj[keys[i]], keys[i]);
          }
          return keys;
        }

        function encodeUriQuery(val, pctEncodeSpaces) {
          return encodeURIComponent(val).
          replace(/%40/gi, '@').
          replace(/%3A/gi, ':').
          replace(/%24/g, '$').
          replace(/%2C/gi, ',').
          replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));
        }

        if (!params) {
          return url + (this.config.suffix || '');
        }

        var parts = [];
        forEachSorted(params, function(value, key) {
          if (value === null || value === undefined) {
            return;
          }
          if (!angular.isArray(value)) {
            value = [value];
          }

          angular.forEach(value, function(v) {
            if (angular.isObject(v)) {
              v = angular.toJson(v);
            }
            parts.push(encodeUriQuery(key) + '=' + encodeUriQuery(v));
          });
        });

        return url + (this.config.suffix || '') + ((url.indexOf('?') === -1) ? '?' : '&') + parts.join('&');
      };

      config.urlCreatorFactory.path = Path;
    };

    var globalConfiguration = {};

    Configurer.init(this, globalConfiguration);



    this.$get = ['$http', '$q', function($http, $q) {

      function createServiceForConfiguration(config) {
        var service = {};

        var urlHandler = new config.urlCreatorFactory[config.urlCreator]();
        urlHandler.setConfig(config);

        function restangularizeBase(parent, elem, route, reqParams, fromServer) {
          elem[config.restangularFields.route] = route;
          elem[config.restangularFields.getRestangularUrl] = _.bind(urlHandler.fetchUrl, urlHandler, elem);
          elem[config.restangularFields.getRequestedUrl] = _.bind(urlHandler.fetchRequestedUrl, urlHandler, elem);
          elem[config.restangularFields.addRestangularMethod] = _.bind(addRestangularMethodFunction, elem);
          elem[config.restangularFields.clone] = _.bind(copyRestangularizedElement, elem, elem);
          elem[config.restangularFields.reqParams] = _.isEmpty(reqParams) ? null : reqParams;
          elem[config.restangularFields.withHttpConfig] = _.bind(withHttpConfig, elem);
          elem[config.restangularFields.plain] = _.bind(stripRestangular, elem, elem);

          // Tag element as restangularized
          elem[config.restangularFields.restangularized] = true;

          // RequestLess connection
          elem[config.restangularFields.one] = _.bind(one, elem, elem);
          elem[config.restangularFields.all] = _.bind(all, elem, elem);
          elem[config.restangularFields.several] = _.bind(several, elem, elem);
          elem[config.restangularFields.oneUrl] = _.bind(oneUrl, elem, elem);
          elem[config.restangularFields.allUrl] = _.bind(allUrl, elem, elem);

          elem[config.restangularFields.fromServer] = !!fromServer;

          if (parent && config.shouldSaveParent(route)) {
            var parentId = config.getIdFromElem(parent);
            var parentUrl = config.getUrlFromElem(parent);

            var restangularFieldsForParent = _.union(
              _.values(_.pick(config.restangularFields, ['route', 'singleOne', 'parentResource'])),
              config.extraFields
            );
            var parentResource = _.pick(parent, restangularFieldsForParent);

            if (config.isValidId(parentId)) {
              config.setIdToElem(parentResource, parentId, route);
            }
            if (config.isValidId(parentUrl)) {
              config.setUrlToElem(parentResource, parentUrl, route);
            }

            elem[config.restangularFields.parentResource] = parentResource;
          } else {
            elem[config.restangularFields.parentResource] = null;
          }
          return elem;
        }

        function one(parent, route, id, singleOne) {
          var error;
          if (_.isNumber(route) || _.isNumber(parent)) {
            error = 'You\'re creating a Restangular entity with the number ';
            error += 'instead of the route or the parent. For example, you can\'t call .one(12).';
            throw new Error(error);
          }
          if (_.isUndefined(route)) {
            error = 'You\'re creating a Restangular entity either without the path. ';
            error += 'For example you can\'t call .one(). Please check if your arguments are valid.';
            throw new Error(error);
          }
          var elem = {};
          config.setIdToElem(elem, id, route);
          config.setFieldToElem(config.restangularFields.singleOne, elem, singleOne);
          return restangularizeElem(parent, elem, route, false);
        }


        function all(parent, route) {
          return restangularizeCollection(parent, [], route, false);
        }

        function several(parent, route /*, ids */ ) {
          var collection = [];
          collection[config.restangularFields.ids] = Array.prototype.splice.call(arguments, 2);
          return restangularizeCollection(parent, collection, route, false);
        }

        function oneUrl(parent, route, url) {
          if (!route) {
            throw new Error('Route is mandatory when creating new Restangular objects.');
          }
          var elem = {};
          config.setUrlToElem(elem, url, route);
          return restangularizeElem(parent, elem, route, false);
        }


        function allUrl(parent, route, url) {
          if (!route) {
            throw new Error('Route is mandatory when creating new Restangular objects.');
          }
          var elem = {};
          config.setUrlToElem(elem, url, route);
          return restangularizeCollection(parent, elem, route, false);
        }
        // Promises
        function restangularizePromise(promise, isCollection, valueToFill) {
          promise.call = _.bind(promiseCall, promise);
          promise.get = _.bind(promiseGet, promise);
          promise[config.restangularFields.restangularCollection] = isCollection;
          if (isCollection) {
            promise.push = _.bind(promiseCall, promise, 'push');
          }
          promise.$object = valueToFill;
          if (config.restangularizePromiseInterceptor) {
            config.restangularizePromiseInterceptor(promise);
          }
          return promise;
        }

        function promiseCall(method) {
          var deferred = $q.defer();
          var callArgs = arguments;
          var filledValue = {};
          this.then(function(val) {
            var params = Array.prototype.slice.call(callArgs, 1);
            var func = val[method];
            func.apply(val, params);
            filledValue = val;
            deferred.resolve(val);
          });
          return restangularizePromise(deferred.promise, this[config.restangularFields.restangularCollection], filledValue);
        }

        function promiseGet(what) {
          var deferred = $q.defer();
          var filledValue = {};
          this.then(function(val) {
            filledValue = val[what];
            deferred.resolve(filledValue);
          });
          return restangularizePromise(deferred.promise, this[config.restangularFields.restangularCollection], filledValue);
        }

        function resolvePromise(deferred, response, data, filledValue) {
          _.extend(filledValue, data);

          // Trigger the full response interceptor.
          if (config.fullResponse) {
            return deferred.resolve(_.extend(response, {
              data: data
            }));
          } else {
            deferred.resolve(data);
          }
        }


        // Elements
        function stripRestangular(elem) {
          if (_.isArray(elem)) {
            var array = [];
            _.each(elem, function(value) {
              array.push(config.isRestangularized(value) ? stripRestangular(value) : value);
            });
            return array;
          } else {
            return _.omit(elem, _.values(_.omit(config.restangularFields, 'id')));
          }
        }

        function addCustomOperation(elem) {
          elem[config.restangularFields.customOperation] = _.bind(customFunction, elem);
          var requestMethods = {
            get: customFunction,
            delete: customFunction
          };
          _.each(['put', 'patch', 'post'], function(name) {
            requestMethods[name] = function(operation, elem, path, params, headers) {
              return _.bind(customFunction, this)(operation, path, params, headers, elem);
            };
          });
          _.each(requestMethods, function(requestFunc, name) {
            var callOperation = name === 'delete' ? 'remove' : name;
            _.each(['do', 'custom'], function(alias) {
              elem[alias + name.toUpperCase()] = _.bind(requestFunc, elem, callOperation);
            });
          });
          elem[config.restangularFields.customGETLIST] = _.bind(fetchFunction, elem);
          elem[config.restangularFields.doGETLIST] = elem[config.restangularFields.customGETLIST];
        }

        function copyRestangularizedElement(element) {
          var copiedElement = angular.copy(element);

          // check if we're dealing with a collection (i.e. an array)
          // and restangularize the element using the proper restangularizer,
          // element / collection
          if (_.isArray(element)) {
            return restangularizeCollection(
              element[config.restangularFields.parentResource],
              copiedElement,
              element[config.restangularFields.route],
              element[config.restangularFields.fromServer],
              element[config.restangularFields.reqParams]
            );
          }

          // not a collection, restangularize it as an element
          return restangularizeElem(
            element[config.restangularFields.parentResource],
            copiedElement,
            element[config.restangularFields.route],
            element[config.restangularFields.fromServer],
            element[config.restangularFields.restangularCollection],
            element[config.restangularFields.reqParams]
          );
        }

        function restangularizeElem(parent, element, route, fromServer, collection, reqParams) {
          var elem = config.onBeforeElemRestangularized(element, false, route);

          var localElem = restangularizeBase(parent, elem, route, reqParams, fromServer);

          if (config.useCannonicalId) {
            localElem[config.restangularFields.cannonicalId] = config.getIdFromElem(localElem);
          }

          if (collection) {
            localElem[config.restangularFields.getParentList] = function() {
              return collection;
            };
          }

          localElem[config.restangularFields.restangularCollection] = false;
          localElem[config.restangularFields.get] = _.bind(getFunction, localElem);
          localElem[config.restangularFields.getList] = _.bind(fetchFunction, localElem);
          localElem[config.restangularFields.put] = _.bind(putFunction, localElem);
          localElem[config.restangularFields.post] = _.bind(postFunction, localElem);
          localElem[config.restangularFields.remove] = _.bind(deleteFunction, localElem);
          localElem[config.restangularFields.head] = _.bind(headFunction, localElem);
          localElem[config.restangularFields.trace] = _.bind(traceFunction, localElem);
          localElem[config.restangularFields.options] = _.bind(optionsFunction, localElem);
          localElem[config.restangularFields.patch] = _.bind(patchFunction, localElem);
          localElem[config.restangularFields.save] = _.bind(save, localElem);

          addCustomOperation(localElem);
          return config.transformElem(localElem, false, route, service, true);
        }

        function restangularizeCollection(parent, element, route, fromServer, reqParams) {
          var elem = config.onBeforeElemRestangularized(element, true, route);

          var localElem = restangularizeBase(parent, elem, route, reqParams, fromServer);
          localElem[config.restangularFields.restangularCollection] = true;
          localElem[config.restangularFields.post] = _.bind(postFunction, localElem, null);
          localElem[config.restangularFields.remove] = _.bind(deleteFunction, localElem);
          localElem[config.restangularFields.head] = _.bind(headFunction, localElem);
          localElem[config.restangularFields.trace] = _.bind(traceFunction, localElem);
          localElem[config.restangularFields.putElement] = _.bind(putElementFunction, localElem);
          localElem[config.restangularFields.options] = _.bind(optionsFunction, localElem);
          localElem[config.restangularFields.patch] = _.bind(patchFunction, localElem);
          localElem[config.restangularFields.get] = _.bind(getById, localElem);
          localElem[config.restangularFields.getList] = _.bind(fetchFunction, localElem, null);

          addCustomOperation(localElem);
          return config.transformElem(localElem, true, route, service, true);
        }

        function restangularizeCollectionAndElements(parent, element, route, fromServer) {
          var collection = restangularizeCollection(parent, element, route, fromServer);
          _.each(collection, function(elem) {
            if (elem) {
              restangularizeElem(parent, elem, route, fromServer);
            }
          });
          return collection;
        }

        function getById(id, reqParams, headers) {
          return this.customGET(id.toString(), reqParams, headers);
        }

        function putElementFunction(idx, params, headers) {
          var __this = this;
          var elemToPut = this[idx];
          var deferred = $q.defer();
          var filledArray = [];
          filledArray = config.transformElem(filledArray, true, elemToPut[config.restangularFields.route], service);
          elemToPut.put(params, headers).then(function(serverElem) {
            var newArray = copyRestangularizedElement(__this);
            newArray[idx] = serverElem;
            filledArray = newArray;
            deferred.resolve(newArray);
          }, function(response) {
            deferred.reject(response);
          });

          return restangularizePromise(deferred.promise, true, filledArray);
        }

        function parseResponse(resData, operation, route, fetchUrl, response, deferred) {
          var data = config.responseExtractor(resData, operation, route, fetchUrl, response, deferred);
          var etag = response.headers('ETag');
          if (data && etag) {
            data[config.restangularFields.etag] = etag;
          }
          return data;
        }


        function fetchFunction(what, reqParams, headers) {
          var __this = this;
          var deferred = $q.defer();
          var operation = 'getList';
          var url = urlHandler.fetchUrl(this, what);
          var whatFetched = what || __this[config.restangularFields.route];

          var request = config.fullRequestInterceptor(null, operation,
            whatFetched, url, headers || {}, reqParams || {}, this[config.restangularFields.httpConfig] || {});

          var filledArray = [];
          filledArray = config.transformElem(filledArray, true, whatFetched, service);

          var method = 'getList';

          if (config.jsonp) {
            method = 'jsonp';
          }

          var okCallback = function(response) {
            var resData = response.data;
            var fullParams = response.config.params;
            var data = parseResponse(resData, operation, whatFetched, url, response, deferred);

            // support empty response for getList() calls (some APIs respond with 204 and empty body)
            if (_.isUndefined(data) || '' === data) {
              data = [];
            }
            if (!_.isArray(data)) {
              throw new Error('Response for getList SHOULD be an array and not an object or something else');
            }

            if (true === config.plainByDefault) {
              return resolvePromise(deferred, response, data, filledArray);
            }

            var processedData = _.map(data, function(elem) {
              if (!__this[config.restangularFields.restangularCollection]) {
                return restangularizeElem(__this, elem, what, true, data);
              } else {
                return restangularizeElem(__this[config.restangularFields.parentResource],
                  elem, __this[config.restangularFields.route], true, data);
              }
            });

            processedData = _.extend(data, processedData);

            if (!__this[config.restangularFields.restangularCollection]) {
              resolvePromise(
                deferred,
                response,
                restangularizeCollection(
                  __this,
                  processedData,
                  what,
                  true,
                  fullParams
                ),
                filledArray
              );
            } else {
              resolvePromise(
                deferred,
                response,
                restangularizeCollection(
                  __this[config.restangularFields.parentResource],
                  processedData,
                  __this[config.restangularFields.route],
                  true,
                  fullParams
                ),
                filledArray
              );
            }
          };

          urlHandler.resource(this, $http, request.httpConfig, request.headers, request.params, what,
            this[config.restangularFields.etag], operation)[method]().then(okCallback, function error(response) {
            if (response.status === 304 && __this[config.restangularFields.restangularCollection]) {
              resolvePromise(deferred, response, __this, filledArray);
            } else if (_.every(config.errorInterceptors, function(cb) {
                return cb(response, deferred, okCallback) !== false;
              })) {
              // triggered if no callback returns false
              deferred.reject(response);
            }
          });

          return restangularizePromise(deferred.promise, true, filledArray);
        }

        function withHttpConfig(httpConfig) {
          this[config.restangularFields.httpConfig] = httpConfig;
          return this;
        }

        function save(params, headers) {
          if (this[config.restangularFields.fromServer]) {
            return this[config.restangularFields.put](params, headers);
          } else {
            return _.bind(elemFunction, this)('post', undefined, params, undefined, headers);
          }
        }

        function elemFunction(operation, what, params, obj, headers) {
          var __this = this;
          var deferred = $q.defer();
          var resParams = params || {};
          var route = what || this[config.restangularFields.route];
          var fetchUrl = urlHandler.fetchUrl(this, what);

          var callObj = obj || this;
          // fallback to etag on restangular object (since for custom methods we probably don't explicitly specify the etag field)
          var etag = callObj[config.restangularFields.etag] || (operation !== 'post' ? this[config.restangularFields.etag] : null);

          if (_.isObject(callObj) && config.isRestangularized(callObj)) {
            callObj = stripRestangular(callObj);
          }
          var request = config.fullRequestInterceptor(callObj, operation, route, fetchUrl,
            headers || {}, resParams || {}, this[config.restangularFields.httpConfig] || {});

          var filledObject = {};
          filledObject = config.transformElem(filledObject, false, route, service);

          var okCallback = function(response) {
            var resData = response.data;
            var fullParams = response.config.params;
            var elem = parseResponse(resData, operation, route, fetchUrl, response, deferred);

            // accept 0 as response
            if (elem !== null && elem !== undefined && elem !== '') {
              var data;

              if (true === config.plainByDefault) {
                return resolvePromise(deferred, response, elem, filledObject);
              }

              if (operation === 'post' && !__this[config.restangularFields.restangularCollection]) {
                data = restangularizeElem(
                  __this[config.restangularFields.parentResource],
                  elem,
                  route,
                  true,
                  null,
                  fullParams
                );
                resolvePromise(deferred, response, data, filledObject);
              } else {
                data = restangularizeElem(
                  __this[config.restangularFields.parentResource],
                  elem,
                  __this[config.restangularFields.route],
                  true,
                  null,
                  fullParams
                );

                data[config.restangularFields.singleOne] = __this[config.restangularFields.singleOne];
                resolvePromise(deferred, response, data, filledObject);
              }

            } else {
              resolvePromise(deferred, response, undefined, filledObject);
            }
          };

          var errorCallback = function(response) {
            if (response.status === 304 && config.isSafe(operation)) {
              resolvePromise(deferred, response, __this, filledObject);
            } else if (_.every(config.errorInterceptors, function(cb) {
                return cb(response, deferred, okCallback) !== false;
              })) {
              // triggered if no callback returns false
              deferred.reject(response);
            }
          };
          // Overriding HTTP Method
          var callOperation = operation;
          var callHeaders = _.extend({}, request.headers);
          var isOverrideOperation = config.isOverridenMethod(operation);
          if (isOverrideOperation) {
            callOperation = 'post';
            callHeaders = _.extend(callHeaders, {
              'X-HTTP-Method-Override': operation === 'remove' ? 'DELETE' : operation.toUpperCase()
            });
          } else if (config.jsonp && callOperation === 'get') {
            callOperation = 'jsonp';
          }

          if (config.isSafe(operation)) {
            if (isOverrideOperation) {
              urlHandler.resource(this, $http, request.httpConfig, callHeaders, request.params,
                what, etag, callOperation)[callOperation]({}).then(okCallback, errorCallback);
            } else {
              urlHandler.resource(this, $http, request.httpConfig, callHeaders, request.params,
                what, etag, callOperation)[callOperation]().then(okCallback, errorCallback);
            }
          } else {
            urlHandler.resource(this, $http, request.httpConfig, callHeaders, request.params,
              what, etag, callOperation)[callOperation](request.element).then(okCallback, errorCallback);
          }

          return restangularizePromise(deferred.promise, false, filledObject);
        }

        function getFunction(params, headers) {
          return _.bind(elemFunction, this)('get', undefined, params, undefined, headers);
        }

        function deleteFunction(params, headers) {
          return _.bind(elemFunction, this)('remove', undefined, params, undefined, headers);
        }

        function putFunction(params, headers) {
          return _.bind(elemFunction, this)('put', undefined, params, undefined, headers);
        }

        function postFunction(what, elem, params, headers) {
          return _.bind(elemFunction, this)('post', what, params, elem, headers);
        }

        function headFunction(params, headers) {
          return _.bind(elemFunction, this)('head', undefined, params, undefined, headers);
        }

        function traceFunction(params, headers) {
          return _.bind(elemFunction, this)('trace', undefined, params, undefined, headers);
        }

        function optionsFunction(params, headers) {
          return _.bind(elemFunction, this)('options', undefined, params, undefined, headers);
        }

        function patchFunction(elem, params, headers) {
          return _.bind(elemFunction, this)('patch', undefined, params, elem, headers);
        }

        function customFunction(operation, path, params, headers, elem) {
          return _.bind(elemFunction, this)(operation, path, params, elem, headers);
        }

        function addRestangularMethodFunction(name, operation, path, defaultParams, defaultHeaders, defaultElem) {
          var bindedFunction;
          if (operation === 'getList') {
            bindedFunction = _.bind(fetchFunction, this, path);
          } else {
            bindedFunction = _.bind(customFunction, this, operation, path);
          }

          var createdFunction = function(params, headers, elem) {
            var callParams = _.defaults({
              params: params,
              headers: headers,
              elem: elem
            }, {
              params: defaultParams,
              headers: defaultHeaders,
              elem: defaultElem
            });
            return bindedFunction(callParams.params, callParams.headers, callParams.elem);
          };

          if (config.isSafe(operation)) {
            this[name] = createdFunction;
          } else {
            this[name] = function(elem, params, headers) {
              return createdFunction(params, headers, elem);
            };
          }
        }

        function withConfigurationFunction(configurer) {
          var newConfig = angular.copy(_.omit(config, 'configuration'));
          Configurer.init(newConfig, newConfig);
          configurer(newConfig);
          return createServiceForConfiguration(newConfig);
        }

        function toService(route, parent) {
          var knownCollectionMethods = _.values(config.restangularFields);
          var serv = {};
          var collection = (parent || service).all(route);
          serv.one = _.bind(one, (parent || service), parent, route);
          serv.post = _.bind(collection.post, collection);
          serv.getList = _.bind(collection.getList, collection);
          serv.withHttpConfig = _.bind(collection.withHttpConfig, collection);
          serv.get = _.bind(collection.get, collection);

          for (var prop in collection) {
            if (collection.hasOwnProperty(prop) && _.isFunction(collection[prop]) && !_.includes(knownCollectionMethods, prop)) {
              serv[prop] = _.bind(collection[prop], collection);
            }
          }

          return serv;
        }


        Configurer.init(service, config);

        service.copy = _.bind(copyRestangularizedElement, service);

        service.service = _.bind(toService, service);

        service.withConfig = _.bind(withConfigurationFunction, service);

        service.one = _.bind(one, service, null);

        service.all = _.bind(all, service, null);

        service.several = _.bind(several, service, null);

        service.oneUrl = _.bind(oneUrl, service, null);

        service.allUrl = _.bind(allUrl, service, null);

        service.stripRestangular = _.bind(stripRestangular, service);

        service.restangularizeElement = _.bind(restangularizeElem, service);

        service.restangularizeCollection = _.bind(restangularizeCollectionAndElements, service);

        return service;
      }

      return createServiceForConfiguration(globalConfiguration);
    }];
  });
  return restangular.name;
}));

/*!
 * angular-translate - v2.15.2 - 2017-06-22
 * 
 * Copyright (c) 2017 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

/**
 * @ngdoc overview
 * @name pascalprecht.translate
 *
 * @description
 * The main module which holds everything together.
 */
runTranslate.$inject = ['$translate'];
$translate.$inject = ['$STORAGE_KEY', '$windowProvider', '$translateSanitizationProvider', 'pascalprechtTranslateOverrider'];
$translateDefaultInterpolation.$inject = ['$interpolate', '$translateSanitization'];
translateDirective.$inject = ['$translate', '$interpolate', '$compile', '$parse', '$rootScope'];
translateAttrDirective.$inject = ['$translate', '$rootScope'];
translateCloakDirective.$inject = ['$translate', '$rootScope'];
translateFilterFactory.$inject = ['$parse', '$translate'];
$translationCache.$inject = ['$cacheFactory'];
angular.module('pascalprecht.translate', ['ng'])
  .run(runTranslate);

function runTranslate($translate) {

  'use strict';

  var key = $translate.storageKey(),
    storage = $translate.storage();

  var fallbackFromIncorrectStorageValue = function () {
    var preferred = $translate.preferredLanguage();
    if (angular.isString(preferred)) {
      $translate.use(preferred);
      // $translate.use() will also remember the language.
      // So, we don't need to call storage.put() here.
    } else {
      storage.put(key, $translate.use());
    }
  };

  fallbackFromIncorrectStorageValue.displayName = 'fallbackFromIncorrectStorageValue';

  if (storage) {
    if (!storage.get(key)) {
      fallbackFromIncorrectStorageValue();
    } else {
      $translate.use(storage.get(key))['catch'](fallbackFromIncorrectStorageValue);
    }
  } else if (angular.isString($translate.preferredLanguage())) {
    $translate.use($translate.preferredLanguage());
  }
}

runTranslate.displayName = 'runTranslate';

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateSanitizationProvider
 *
 * @description
 *
 * Configurations for $translateSanitization
 */
angular.module('pascalprecht.translate').provider('$translateSanitization', $translateSanitizationProvider);

function $translateSanitizationProvider () {

  'use strict';

  var $sanitize,
      $sce,
      currentStrategy = null, // TODO change to either 'sanitize', 'escape' or ['sanitize', 'escapeParameters'] in 3.0.
      hasConfiguredStrategy = false,
      hasShownNoStrategyConfiguredWarning = false,
      strategies;

  /**
   * Definition of a sanitization strategy function
   * @callback StrategyFunction
   * @param {string|object} value - value to be sanitized (either a string or an interpolated value map)
   * @param {string} mode - either 'text' for a string (translation) or 'params' for the interpolated params
   * @return {string|object}
   */

  /**
   * @ngdoc property
   * @name strategies
   * @propertyOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Following strategies are built-in:
   * <dl>
   *   <dt>sanitize</dt>
   *   <dd>Sanitizes HTML in the translation text using $sanitize</dd>
   *   <dt>escape</dt>
   *   <dd>Escapes HTML in the translation</dd>
   *   <dt>sanitizeParameters</dt>
   *   <dd>Sanitizes HTML in the values of the interpolation parameters using $sanitize</dd>
   *   <dt>escapeParameters</dt>
   *   <dd>Escapes HTML in the values of the interpolation parameters</dd>
   *   <dt>escaped</dt>
   *   <dd>Support legacy strategy name 'escaped' for backwards compatibility (will be removed in 3.0)</dd>
   * </dl>
   *
   */

  strategies = {
    sanitize: function (value, mode/*, context*/) {
      if (mode === 'text') {
        value = htmlSanitizeValue(value);
      }
      return value;
    },
    escape: function (value, mode/*, context*/) {
      if (mode === 'text') {
        value = htmlEscapeValue(value);
      }
      return value;
    },
    sanitizeParameters: function (value, mode/*, context*/) {
      if (mode === 'params') {
        value = mapInterpolationParameters(value, htmlSanitizeValue);
      }
      return value;
    },
    escapeParameters: function (value, mode/*, context*/) {
      if (mode === 'params') {
        value = mapInterpolationParameters(value, htmlEscapeValue);
      }
      return value;
    },
    sce: function (value, mode, context) {
      if (mode === 'text') {
        value = htmlTrustValue(value);
      } else if (mode === 'params') {
        if (context !== 'filter') {
          // do html escape in filter context #1101
          value = mapInterpolationParameters(value, htmlEscapeValue);
        }
      }
      return value;
    },
    sceParameters: function (value, mode/*, context*/) {
      if (mode === 'params') {
        value = mapInterpolationParameters(value, htmlTrustValue);
      }
      return value;
    }
  };
  // Support legacy strategy name 'escaped' for backwards compatibility.
  // TODO should be removed in 3.0
  strategies.escaped = strategies.escapeParameters;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#addStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Adds a sanitization strategy to the list of known strategies.
   *
   * @param {string} strategyName - unique key for a strategy
   * @param {StrategyFunction} strategyFunction - strategy function
   * @returns {object} this
   */
  this.addStrategy = function (strategyName, strategyFunction) {
    strategies[strategyName] = strategyFunction;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#removeStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Removes a sanitization strategy from the list of known strategies.
   *
   * @param {string} strategyName - unique key for a strategy
   * @returns {object} this
   */
  this.removeStrategy = function (strategyName) {
    delete strategies[strategyName];
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateSanitizationProvider#useStrategy
   * @methodOf pascalprecht.translate.$translateSanitizationProvider
   *
   * @description
   * Selects a sanitization strategy. When an array is provided the strategies will be executed in order.
   *
   * @param {string|StrategyFunction|array} strategy The sanitization strategy / strategies which should be used. Either a name of an existing strategy, a custom strategy function, or an array consisting of multiple names and / or custom functions.
   * @returns {object} this
   */
  this.useStrategy = function (strategy) {
    hasConfiguredStrategy = true;
    currentStrategy = strategy;
    return this;
  };

  /**
   * @ngdoc object
   * @name pascalprecht.translate.$translateSanitization
   * @requires $injector
   * @requires $log
   *
   * @description
   * Sanitizes interpolation parameters and translated texts.
   *
   */
  this.$get = ['$injector', '$log', function ($injector, $log) {

    var cachedStrategyMap = {};

    var applyStrategies = function (value, mode, context, selectedStrategies) {
      angular.forEach(selectedStrategies, function (selectedStrategy) {
        if (angular.isFunction(selectedStrategy)) {
          value = selectedStrategy(value, mode, context);
        } else if (angular.isFunction(strategies[selectedStrategy])) {
          value = strategies[selectedStrategy](value, mode, context);
        } else if (angular.isString(strategies[selectedStrategy])) {
          if (!cachedStrategyMap[strategies[selectedStrategy]]) {
            try {
              cachedStrategyMap[strategies[selectedStrategy]] = $injector.get(strategies[selectedStrategy]);
            } catch (e) {
              cachedStrategyMap[strategies[selectedStrategy]] = function() {};
              throw new Error('pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: \'' + selectedStrategy + '\'');
            }
          }
          value = cachedStrategyMap[strategies[selectedStrategy]](value, mode, context);
        } else {
          throw new Error('pascalprecht.translate.$translateSanitization: Unknown sanitization strategy: \'' + selectedStrategy + '\'');
        }
      });
      return value;
    };

    // TODO: should be removed in 3.0
    var showNoStrategyConfiguredWarning = function () {
      if (!hasConfiguredStrategy && !hasShownNoStrategyConfiguredWarning) {
        $log.warn('pascalprecht.translate.$translateSanitization: No sanitization strategy has been configured. This can have serious security implications. See http://angular-translate.github.io/docs/#/guide/19_security for details.');
        hasShownNoStrategyConfiguredWarning = true;
      }
    };

    if ($injector.has('$sanitize')) {
      $sanitize = $injector.get('$sanitize');
    }
    if ($injector.has('$sce')) {
      $sce = $injector.get('$sce');
    }

    return {
      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateSanitization#useStrategy
       * @methodOf pascalprecht.translate.$translateSanitization
       *
       * @description
       * Selects a sanitization strategy. When an array is provided the strategies will be executed in order.
       *
       * @param {string|StrategyFunction|array} strategy The sanitization strategy / strategies which should be used. Either a name of an existing strategy, a custom strategy function, or an array consisting of multiple names and / or custom functions.
       */
      useStrategy: (function (self) {
        return function (strategy) {
          self.useStrategy(strategy);
        };
      })(this),

      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateSanitization#sanitize
       * @methodOf pascalprecht.translate.$translateSanitization
       *
       * @description
       * Sanitizes a value.
       *
       * @param {string|object} value The value which should be sanitized.
       * @param {string} mode The current sanitization mode, either 'params' or 'text'.
       * @param {string|StrategyFunction|array} [strategy] Optional custom strategy which should be used instead of the currently selected strategy.
       * @param {string} [context] The context of this call: filter, service. Default is service
       * @returns {string|object} sanitized value
       */
      sanitize: function (value, mode, strategy, context) {
        if (!currentStrategy) {
          showNoStrategyConfiguredWarning();
        }

        if (!strategy && strategy !== null) {
          strategy = currentStrategy;
        }

        if (!strategy) {
          return value;
        }

        if (!context) {
          context = 'service';
        }

        var selectedStrategies = angular.isArray(strategy) ? strategy : [strategy];
        return applyStrategies(value, mode, context, selectedStrategies);
      }
    };
  }];

  var htmlEscapeValue = function (value) {
    var element = angular.element('<div></div>');
    element.text(value); // not chainable, see #1044
    return element.html();
  };

  var htmlSanitizeValue = function (value) {
    if (!$sanitize) {
      throw new Error('pascalprecht.translate.$translateSanitization: Error cannot find $sanitize service. Either include the ngSanitize module (https://docs.angularjs.org/api/ngSanitize) or use a sanitization strategy which does not depend on $sanitize, such as \'escape\'.');
    }
    return $sanitize(value);
  };

  var htmlTrustValue = function (value) {
    if (!$sce) {
      throw new Error('pascalprecht.translate.$translateSanitization: Error cannot find $sce service.');
    }
    return $sce.trustAsHtml(value);
  };

  var mapInterpolationParameters = function (value, iteratee, stack) {
    if (angular.isDate(value)) {
      return value;
    } else if (angular.isObject(value)) {
      var result = angular.isArray(value) ? [] : {};

      if (!stack) {
        stack = [];
      } else {
        if (stack.indexOf(value) > -1) {
          throw new Error('pascalprecht.translate.$translateSanitization: Error cannot interpolate parameter due recursive object');
        }
      }

      stack.push(value);
      angular.forEach(value, function (propertyValue, propertyKey) {

        /* Skipping function properties. */
        if (angular.isFunction(propertyValue)) {
          return;
        }

        result[propertyKey] = mapInterpolationParameters(propertyValue, iteratee, stack);
      });
      stack.splice(-1, 1); // remove last

      return result;
    } else if (angular.isNumber(value)) {
      return value;
    } else if (value === true || value === false) {
      return value;
    } else if (!angular.isUndefined(value) && value !== null) {
      return iteratee(value);
    } else {
      return value;
    }
  };
}

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateProvider
 * @description
 *
 * $translateProvider allows developers to register translation-tables, asynchronous loaders
 * and similar to configure translation behavior directly inside of a module.
 *
 */
angular.module('pascalprecht.translate')
  .constant('pascalprechtTranslateOverrider', {})
  .provider('$translate', $translate);

function $translate($STORAGE_KEY, $windowProvider, $translateSanitizationProvider, pascalprechtTranslateOverrider) {

  'use strict';

  var $translationTable = {},
    $preferredLanguage,
    $availableLanguageKeys = [],
    $languageKeyAliases,
    $fallbackLanguage,
    $fallbackWasString,
    $uses,
    $nextLang,
    $storageFactory,
    $storageKey = $STORAGE_KEY,
    $storagePrefix,
    $missingTranslationHandlerFactory,
    $interpolationFactory,
    $interpolatorFactories = [],
    $loaderFactory,
    $cloakClassName = 'translate-cloak',
    $loaderOptions,
    $notFoundIndicatorLeft,
    $notFoundIndicatorRight,
    $postCompilingEnabled = false,
    $forceAsyncReloadEnabled = false,
    $nestedObjectDelimeter = '.',
    $isReady = false,
    $keepContent = false,
    loaderCache,
    directivePriority = 0,
    statefulFilter = true,
    postProcessFn,
    uniformLanguageTagResolver = 'default',
    languageTagResolver = {
      'default' : function (tag) {
        return (tag || '').split('-').join('_');
      },
      java : function (tag) {
        var temp = (tag || '').split('-').join('_');
        var parts = temp.split('_');
        return parts.length > 1 ? (parts[0].toLowerCase() + '_' + parts[1].toUpperCase()) : temp;
      },
      bcp47 : function (tag) {
        var temp = (tag || '').split('_').join('-');
        var parts = temp.split('-');
        return parts.length > 1 ? (parts[0].toLowerCase() + '-' + parts[1].toUpperCase()) : temp;
      },
      'iso639-1' : function (tag) {
        var temp = (tag || '').split('_').join('-');
        var parts = temp.split('-');
        return parts[0].toLowerCase();
      }
    };

  var version = '2.15.2';

  // tries to determine the browsers language
  var getFirstBrowserLanguage = function () {

    // internal purpose only
    if (angular.isFunction(pascalprechtTranslateOverrider.getLocale)) {
      return pascalprechtTranslateOverrider.getLocale();
    }

    var nav = $windowProvider.$get().navigator,
      browserLanguagePropertyKeys = ['language', 'browserLanguage', 'systemLanguage', 'userLanguage'],
      i,
      language;

    // support for HTML 5.1 "navigator.languages"
    if (angular.isArray(nav.languages)) {
      for (i = 0; i < nav.languages.length; i++) {
        language = nav.languages[i];
        if (language && language.length) {
          return language;
        }
      }
    }

    // support for other well known properties in browsers
    for (i = 0; i < browserLanguagePropertyKeys.length; i++) {
      language = nav[browserLanguagePropertyKeys[i]];
      if (language && language.length) {
        return language;
      }
    }

    return null;
  };
  getFirstBrowserLanguage.displayName = 'angular-translate/service: getFirstBrowserLanguage';

  // tries to determine the browsers locale
  var getLocale = function () {
    var locale = getFirstBrowserLanguage() || '';
    if (languageTagResolver[uniformLanguageTagResolver]) {
      locale = languageTagResolver[uniformLanguageTagResolver](locale);
    }
    return locale;
  };
  getLocale.displayName = 'angular-translate/service: getLocale';

  /**
   * @name indexOf
   * @private
   *
   * @description
   * indexOf polyfill. Kinda sorta.
   *
   * @param {array} array Array to search in.
   * @param {string} searchElement Element to search for.
   *
   * @returns {int} Index of search element.
   */
  var indexOf = function (array, searchElement) {
    for (var i = 0, len = array.length; i < len; i++) {
      if (array[i] === searchElement) {
        return i;
      }
    }
    return -1;
  };

  /**
   * @name trim
   * @private
   *
   * @description
   * trim polyfill
   *
   * @returns {string} The string stripped of whitespace from both ends
   */
  var trim = function () {
    return this.toString().replace(/^\s+|\s+$/g, '');
  };

  var negotiateLocale = function (preferred) {
    if (!preferred) {
      return;
    }

    var avail = [],
      locale = angular.lowercase(preferred),
      i = 0,
      n = $availableLanguageKeys.length;

    for (; i < n; i++) {
      avail.push(angular.lowercase($availableLanguageKeys[i]));
    }

    // Check for an exact match in our list of available keys
    if (indexOf(avail, locale) > -1) {
      return preferred;
    }

    if ($languageKeyAliases) {
      var alias;
      for (var langKeyAlias in $languageKeyAliases) {
        if ($languageKeyAliases.hasOwnProperty(langKeyAlias)) {
          var hasWildcardKey = false;
          var hasExactKey = Object.prototype.hasOwnProperty.call($languageKeyAliases, langKeyAlias) &&
            angular.lowercase(langKeyAlias) === angular.lowercase(preferred);

          if (langKeyAlias.slice(-1) === '*') {
            hasWildcardKey = langKeyAlias.slice(0, -1) === preferred.slice(0, langKeyAlias.length - 1);
          }
          if (hasExactKey || hasWildcardKey) {
            alias = $languageKeyAliases[langKeyAlias];
            if (indexOf(avail, angular.lowercase(alias)) > -1) {
              return alias;
            }
          }
        }
      }
    }

    // Check for a language code without region
    var parts = preferred.split('_');

    if (parts.length > 1 && indexOf(avail, angular.lowercase(parts[0])) > -1) {
      return parts[0];
    }

    // If everything fails, return undefined.
    return;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#translations
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a new translation table for specific language key.
   *
   * To register a translation table for specific language, pass a defined language
   * key as first parameter.
   *
   * <pre>
   *  // register translation table for language: 'de_DE'
   *  $translateProvider.translations('de_DE', {
   *    'GREETING': 'Hallo Welt!'
   *  });
   *
   *  // register another one
   *  $translateProvider.translations('en_US', {
   *    'GREETING': 'Hello world!'
   *  });
   * </pre>
   *
   * When registering multiple translation tables for for the same language key,
   * the actual translation table gets extended. This allows you to define module
   * specific translation which only get added, once a specific module is loaded in
   * your app.
   *
   * Invoking this method with no arguments returns the translation table which was
   * registered with no language key. Invoking it with a language key returns the
   * related translation table.
   *
   * @param {string} langKey A language key.
   * @param {object} translationTable A plain old JavaScript object that represents a translation table.
   *
   */
  var translations = function (langKey, translationTable) {

    if (!langKey && !translationTable) {
      return $translationTable;
    }

    if (langKey && !translationTable) {
      if (angular.isString(langKey)) {
        return $translationTable[langKey];
      }
    } else {
      if (!angular.isObject($translationTable[langKey])) {
        $translationTable[langKey] = {};
      }
      angular.extend($translationTable[langKey], flatObject(translationTable));
    }
    return this;
  };

  this.translations = translations;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#cloakClassName
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   *
   * Let's you change the class name for `translate-cloak` directive.
   * Default class name is `translate-cloak`.
   *
   * @param {string} name translate-cloak class name
   */
  this.cloakClassName = function (name) {
    if (!name) {
      return $cloakClassName;
    }
    $cloakClassName = name;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#nestedObjectDelimeter
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   *
   * Let's you change the delimiter for namespaced translations.
   * Default delimiter is `.`.
   *
   * @param {string} delimiter namespace separator
   */
  this.nestedObjectDelimeter = function (delimiter) {
    if (!delimiter) {
      return $nestedObjectDelimeter;
    }
    $nestedObjectDelimeter = delimiter;
    return this;
  };

  /**
   * @name flatObject
   * @private
   *
   * @description
   * Flats an object. This function is used to flatten given translation data with
   * namespaces, so they are later accessible via dot notation.
   */
  var flatObject = function (data, path, result, prevKey) {
    var key, keyWithPath, keyWithShortPath, val;

    if (!path) {
      path = [];
    }
    if (!result) {
      result = {};
    }
    for (key in data) {
      if (!Object.prototype.hasOwnProperty.call(data, key)) {
        continue;
      }
      val = data[key];
      if (angular.isObject(val)) {
        flatObject(val, path.concat(key), result, key);
      } else {
        keyWithPath = path.length ? ('' + path.join($nestedObjectDelimeter) + $nestedObjectDelimeter + key) : key;
        if (path.length && key === prevKey) {
          // Create shortcut path (foo.bar == foo.bar.bar)
          keyWithShortPath = '' + path.join($nestedObjectDelimeter);
          // Link it to original path
          result[keyWithShortPath] = '@:' + keyWithPath;
        }
        result[keyWithPath] = val;
      }
    }
    return result;
  };
  flatObject.displayName = 'flatObject';

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#addInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Adds interpolation services to angular-translate, so it can manage them.
   *
   * @param {object} factory Interpolation service factory
   */
  this.addInterpolation = function (factory) {
    $interpolatorFactories.push(factory);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMessageFormatInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use interpolation functionality of messageformat.js.
   * This is useful when having high level pluralization and gender selection.
   */
  this.useMessageFormatInterpolation = function () {
    return this.useInterpolation('$translateMessageFormatInterpolation');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useInterpolation
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate which interpolation style to use as default, application-wide.
   * Simply pass a factory/service name. The interpolation service has to implement
   * the correct interface.
   *
   * @param {string} factory Interpolation service name.
   */
  this.useInterpolation = function (factory) {
    $interpolationFactory = factory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useSanitizeStrategy
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Simply sets a sanitation strategy type.
   *
   * @param {string} value Strategy type.
   */
  this.useSanitizeValueStrategy = function (value) {
    $translateSanitizationProvider.useStrategy(value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#preferredLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which of the registered translation tables to use for translation
   * at initial startup by passing a language key. Similar to `$translateProvider#use`
   * only that it says which language to **prefer**.
   *
   * @param {string} langKey A language key.
   */
  this.preferredLanguage = function (langKey) {
    if (langKey) {
      setupPreferredLanguage(langKey);
      return this;
    }
    return $preferredLanguage;
  };
  var setupPreferredLanguage = function (langKey) {
    if (langKey) {
      $preferredLanguage = langKey;
    }
    return $preferredLanguage;
  };
  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicator
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found. E.g. when
   * setting the indicator as 'X' and one tries to translate a translation id
   * called `NOT_FOUND`, this will result in `X NOT_FOUND X`.
   *
   * Internally this methods sets a left indicator and a right indicator using
   * `$translateProvider.translationNotFoundIndicatorLeft()` and
   * `$translateProvider.translationNotFoundIndicatorRight()`.
   *
   * **Note**: These methods automatically add a whitespace between the indicators
   * and the translation id.
   *
   * @param {string} indicator An indicator, could be any string.
   */
  this.translationNotFoundIndicator = function (indicator) {
    this.translationNotFoundIndicatorLeft(indicator);
    this.translationNotFoundIndicatorRight(indicator);
    return this;
  };

  /**
   * ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicatorLeft
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found left to the
   * translation id.
   *
   * @param {string} indicator An indicator.
   */
  this.translationNotFoundIndicatorLeft = function (indicator) {
    if (!indicator) {
      return $notFoundIndicatorLeft;
    }
    $notFoundIndicatorLeft = indicator;
    return this;
  };

  /**
   * ngdoc function
   * @name pascalprecht.translate.$translateProvider#translationNotFoundIndicatorLeft
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets an indicator which is used when a translation isn't found right to the
   * translation id.
   *
   * @param {string} indicator An indicator.
   */
  this.translationNotFoundIndicatorRight = function (indicator) {
    if (!indicator) {
      return $notFoundIndicatorRight;
    }
    $notFoundIndicatorRight = indicator;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#fallbackLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which of the registered translation tables to use when missing translations
   * at initial startup by passing a language key. Similar to `$translateProvider#use`
   * only that it says which language to **fallback**.
   *
   * @param {string||array} langKey A language key.
   *
   */
  this.fallbackLanguage = function (langKey) {
    fallbackStack(langKey);
    return this;
  };

  var fallbackStack = function (langKey) {
    if (langKey) {
      if (angular.isString(langKey)) {
        $fallbackWasString = true;
        $fallbackLanguage = [langKey];
      } else if (angular.isArray(langKey)) {
        $fallbackWasString = false;
        $fallbackLanguage = langKey;
      }
      if (angular.isString($preferredLanguage) && indexOf($fallbackLanguage, $preferredLanguage) < 0) {
        $fallbackLanguage.push($preferredLanguage);
      }

      return this;
    } else {
      if ($fallbackWasString) {
        return $fallbackLanguage[0];
      } else {
        return $fallbackLanguage;
      }
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#use
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Set which translation table to use for translation by given language key. When
   * trying to 'use' a language which isn't provided, it'll throw an error.
   *
   * You actually don't have to use this method since `$translateProvider#preferredLanguage`
   * does the job too.
   *
   * @param {string} langKey A language key.
   */
  this.use = function (langKey) {
    if (langKey) {
      if (!$translationTable[langKey] && (!$loaderFactory)) {
        // only throw an error, when not loading translation data asynchronously
        throw new Error('$translateProvider couldn\'t find translationTable for langKey: \'' + langKey + '\'');
      }
      $uses = langKey;
      return this;
    }
    return $uses;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#resolveClientLocale
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * This returns the current browser/client's language key. The result is processed with the configured uniform tag resolver.
   *
   * @returns {string} the current client/browser language key
   */
  this.resolveClientLocale = function () {
    return getLocale();
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#storageKey
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells the module which key must represent the choosed language by a user in the storage.
   *
   * @param {string} key A key for the storage.
   */
  var storageKey = function (key) {
    if (!key) {
      if ($storagePrefix) {
        return $storagePrefix + $storageKey;
      }
      return $storageKey;
    }
    $storageKey = key;
    return this;
  };

  this.storageKey = storageKey;

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useUrlLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateUrlLoader` extension service as loader.
   *
   * @param {string} url Url
   * @param {Object=} options Optional configuration object
   */
  this.useUrlLoader = function (url, options) {
    return this.useLoader('$translateUrlLoader', angular.extend({url : url}, options));
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useStaticFilesLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateStaticFilesLoader` extension service as loader.
   *
   * @param {Object=} options Optional configuration object
   */
  this.useStaticFilesLoader = function (options) {
    return this.useLoader('$translateStaticFilesLoader', options);
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLoader
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use any other service as loader.
   *
   * @param {string} loaderFactory Factory name to use
   * @param {Object=} options Optional configuration object
   */
  this.useLoader = function (loaderFactory, options) {
    $loaderFactory = loaderFactory;
    $loaderOptions = options || {};
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLocalStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateLocalStorage` service as storage layer.
   *
   */
  this.useLocalStorage = function () {
    return this.useStorage('$translateLocalStorage');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useCookieStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use `$translateCookieStorage` service as storage layer.
   */
  this.useCookieStorage = function () {
    return this.useStorage('$translateCookieStorage');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useStorage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use custom service as storage layer.
   */
  this.useStorage = function (storageFactory) {
    $storageFactory = storageFactory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#storagePrefix
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets prefix for storage key.
   *
   * @param {string} prefix Storage key prefix
   */
  this.storagePrefix = function (prefix) {
    if (!prefix) {
      return prefix;
    }
    $storagePrefix = prefix;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMissingTranslationHandlerLog
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to use built-in log handler when trying to translate
   * a translation Id which doesn't exist.
   *
   * This is actually a shortcut method for `useMissingTranslationHandler()`.
   *
   */
  this.useMissingTranslationHandlerLog = function () {
    return this.useMissingTranslationHandler('$translateMissingTranslationHandlerLog');
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useMissingTranslationHandler
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Expects a factory name which later gets instantiated with `$injector`.
   * This method can be used to tell angular-translate to use a custom
   * missingTranslationHandler. Just build a factory which returns a function
   * and expects a translation id as argument.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.useMissingTranslationHandler('customHandler');
   *  });
   *
   *  app.factory('customHandler', function (dep1, dep2) {
   *    return function (translationId) {
   *      // something with translationId and dep1 and dep2
   *    };
   *  });
   * </pre>
   *
   * @param {string} factory Factory name
   */
  this.useMissingTranslationHandler = function (factory) {
    $missingTranslationHandlerFactory = factory;
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#usePostCompiling
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If post compiling is enabled, all translated values will be processed
   * again with AngularJS' $compile.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.usePostCompiling(true);
   *  });
   * </pre>
   *
   * @param {string} factory Factory name
   */
  this.usePostCompiling = function (value) {
    $postCompilingEnabled = !(!value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#forceAsyncReload
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If force async reload is enabled, async loader will always be called
   * even if $translationTable already contains the language key, adding
   * possible new entries to the $translationTable.
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.forceAsyncReload(true);
   *  });
   * </pre>
   *
   * @param {boolean} value - valid values are true or false
   */
  this.forceAsyncReload = function (value) {
    $forceAsyncReloadEnabled = !(!value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#uniformLanguageTag
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate which language tag should be used as a result when determining
   * the current browser language.
   *
   * This setting must be set before invoking {@link pascalprecht.translate.$translateProvider#methods_determinePreferredLanguage determinePreferredLanguage()}.
   *
   * <pre>
   * $translateProvider
   *   .uniformLanguageTag('bcp47')
   *   .determinePreferredLanguage()
   * </pre>
   *
   * The resolver currently supports:
   * * default
   *     (traditionally: hyphens will be converted into underscores, i.e. en-US => en_US)
   *     en-US => en_US
   *     en_US => en_US
   *     en-us => en_us
   * * java
   *     like default, but the second part will be always in uppercase
   *     en-US => en_US
   *     en_US => en_US
   *     en-us => en_US
   * * BCP 47 (RFC 4646 & 4647)
   *     en-US => en-US
   *     en_US => en-US
   *     en-us => en-US
   *
   * See also:
   * * http://en.wikipedia.org/wiki/IETF_language_tag
   * * http://www.w3.org/International/core/langtags/
   * * http://tools.ietf.org/html/bcp47
   *
   * @param {string|object} options - options (or standard)
   * @param {string} options.standard - valid values are 'default', 'bcp47', 'java'
   */
  this.uniformLanguageTag = function (options) {

    if (!options) {
      options = {};
    } else if (angular.isString(options)) {
      options = {
        standard : options
      };
    }

    uniformLanguageTagResolver = options.standard;

    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#determinePreferredLanguage
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Tells angular-translate to try to determine on its own which language key
   * to set as preferred language. When `fn` is given, angular-translate uses it
   * to determine a language key, otherwise it uses the built-in `getLocale()`
   * method.
   *
   * The `getLocale()` returns a language key in the format `[lang]_[country]` or
   * `[lang]` depending on what the browser provides.
   *
   * Use this method at your own risk, since not all browsers return a valid
   * locale (see {@link pascalprecht.translate.$translateProvider#methods_uniformLanguageTag uniformLanguageTag()}).
   *
   * @param {Function=} fn Function to determine a browser's locale
   */
  this.determinePreferredLanguage = function (fn) {

    var locale = (fn && angular.isFunction(fn)) ? fn() : getLocale();

    if (!$availableLanguageKeys.length) {
      $preferredLanguage = locale;
    } else {
      $preferredLanguage = negotiateLocale(locale) || locale;
    }

    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#registerAvailableLanguageKeys
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a set of language keys the app will work with. Use this method in
   * combination with
   * {@link pascalprecht.translate.$translateProvider#determinePreferredLanguage determinePreferredLanguage}.
   * When available languages keys are registered, angular-translate
   * tries to find the best fitting language key depending on the browsers locale,
   * considering your language key convention.
   *
   * @param {object} languageKeys Array of language keys the your app will use
   * @param {object=} aliases Alias map.
   */
  this.registerAvailableLanguageKeys = function (languageKeys, aliases) {
    if (languageKeys) {
      $availableLanguageKeys = languageKeys;
      if (aliases) {
        $languageKeyAliases = aliases;
      }
      return this;
    }
    return $availableLanguageKeys;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#useLoaderCache
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Registers a cache for internal $http based loaders.
   * {@link pascalprecht.translate.$translationCache $translationCache}.
   * When false the cache will be disabled (default). When true or undefined
   * the cache will be a default (see $cacheFactory). When an object it will
   * be treat as a cache object itself: the usage is $http({cache: cache})
   *
   * @param {object} cache boolean, string or cache-object
   */
  this.useLoaderCache = function (cache) {
    if (cache === false) {
      // disable cache
      loaderCache = undefined;
    } else if (cache === true) {
      // enable cache using AJS defaults
      loaderCache = true;
    } else if (typeof(cache) === 'undefined') {
      // enable cache using default
      loaderCache = '$translationCache';
    } else if (cache) {
      // enable cache using given one (see $cacheFactory)
      loaderCache = cache;
    }
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#directivePriority
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Sets the default priority of the translate directive. The standard value is `0`.
   * Calling this function without an argument will return the current value.
   *
   * @param {number} priority for the translate-directive
   */
  this.directivePriority = function (priority) {
    if (priority === undefined) {
      // getter
      return directivePriority;
    } else {
      // setter with chaining
      directivePriority = priority;
      return this;
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#statefulFilter
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * Since AngularJS 1.3, filters which are not stateless (depending at the scope)
   * have to explicit define this behavior.
   * Sets whether the translate filter should be stateful or stateless. The standard value is `true`
   * meaning being stateful.
   * Calling this function without an argument will return the current value.
   *
   * @param {boolean} state - defines the state of the filter
   */
  this.statefulFilter = function (state) {
    if (state === undefined) {
      // getter
      return statefulFilter;
    } else {
      // setter with chaining
      statefulFilter = state;
      return this;
    }
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#postProcess
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * The post processor will be intercept right after the translation result. It can modify the result.
   *
   * @param {object} fn Function or service name (string) to be called after the translation value has been set / resolved. The function itself will enrich every value being processed and then continue the normal resolver process
   */
  this.postProcess = function (fn) {
    if (fn) {
      postProcessFn = fn;
    } else {
      postProcessFn = undefined;
    }
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateProvider#keepContent
   * @methodOf pascalprecht.translate.$translateProvider
   *
   * @description
   * If keepContent is set to true than translate directive will always use innerHTML
   * as a default translation
   *
   * Example:
   * <pre>
   *  app.config(function ($translateProvider) {
   *    $translateProvider.keepContent(true);
   *  });
   * </pre>
   *
   * @param {boolean} value - valid values are true or false
   */
  this.keepContent = function (value) {
    $keepContent = !(!value);
    return this;
  };

  /**
   * @ngdoc object
   * @name pascalprecht.translate.$translate
   * @requires $interpolate
   * @requires $log
   * @requires $rootScope
   * @requires $q
   *
   * @description
   * The `$translate` service is the actual core of angular-translate. It expects a translation id
   * and optional interpolate parameters to translate contents.
   *
   * <pre>
   *  $translate('HEADLINE_TEXT').then(function (translation) {
   *    $scope.translatedText = translation;
   *  });
   * </pre>
   *
   * @param {string|array} translationId A token which represents a translation id
   *                                     This can be optionally an array of translation ids which
   *                                     results that the function returns an object where each key
   *                                     is the translation id and the value the translation.
   * @param {object=} interpolateParams An object hash for dynamic values
   * @param {string} interpolationId The id of the interpolation to use
   * @param {string} defaultTranslationText the optional default translation text that is written as
   *                                        as default text in case it is not found in any configured language
   * @param {string} forceLanguage A language to be used instead of the current language
   * @returns {object} promise
   */
  this.$get = ['$log', '$injector', '$rootScope', '$q', function ($log, $injector, $rootScope, $q) {

    var Storage,
      defaultInterpolator = $injector.get($interpolationFactory || '$translateDefaultInterpolation'),
      pendingLoader = false,
      interpolatorHashMap = {},
      langPromises = {},
      fallbackIndex,
      startFallbackIteration;

    var $translate = function (translationId, interpolateParams, interpolationId, defaultTranslationText, forceLanguage) {
      if (!$uses && $preferredLanguage) {
        $uses = $preferredLanguage;
      }
      var uses = (forceLanguage && forceLanguage !== $uses) ? // we don't want to re-negotiate $uses
        (negotiateLocale(forceLanguage) || forceLanguage) : $uses;

      // Check forceLanguage is present
      if (forceLanguage) {
        loadTranslationsIfMissing(forceLanguage);
      }

      // Duck detection: If the first argument is an array, a bunch of translations was requested.
      // The result is an object.
      if (angular.isArray(translationId)) {
        // Inspired by Q.allSettled by Kris Kowal
        // https://github.com/kriskowal/q/blob/b0fa72980717dc202ffc3cbf03b936e10ebbb9d7/q.js#L1553-1563
        // This transforms all promises regardless resolved or rejected
        var translateAll = function (translationIds) {
          var results = {}; // storing the actual results
          var promises = []; // promises to wait for
          // Wraps the promise a) being always resolved and b) storing the link id->value
          var translate = function (translationId) {
            var deferred = $q.defer();
            var regardless = function (value) {
              results[translationId] = value;
              deferred.resolve([translationId, value]);
            };
            // we don't care whether the promise was resolved or rejected; just store the values
            $translate(translationId, interpolateParams, interpolationId, defaultTranslationText, forceLanguage).then(regardless, regardless);
            return deferred.promise;
          };
          for (var i = 0, c = translationIds.length; i < c; i++) {
            promises.push(translate(translationIds[i]));
          }
          // wait for all (including storing to results)
          return $q.all(promises).then(function () {
            // return the results
            return results;
          });
        };
        return translateAll(translationId);
      }

      var deferred = $q.defer();

      // trim off any whitespace
      if (translationId) {
        translationId = trim.apply(translationId);
      }

      var promiseToWaitFor = (function () {
        var promise = $preferredLanguage ?
          langPromises[$preferredLanguage] :
          langPromises[uses];

        fallbackIndex = 0;

        if ($storageFactory && !promise) {
          // looks like there's no pending promise for $preferredLanguage or
          // $uses. Maybe there's one pending for a language that comes from
          // storage.
          var langKey = Storage.get($storageKey);
          promise = langPromises[langKey];

          if ($fallbackLanguage && $fallbackLanguage.length) {
            var index = indexOf($fallbackLanguage, langKey);
            // maybe the language from storage is also defined as fallback language
            // we increase the fallback language index to not search in that language
            // as fallback, since it's probably the first used language
            // in that case the index starts after the first element
            fallbackIndex = (index === 0) ? 1 : 0;

            // but we can make sure to ALWAYS fallback to preferred language at least
            if (indexOf($fallbackLanguage, $preferredLanguage) < 0) {
              $fallbackLanguage.push($preferredLanguage);
            }
          }
        }
        return promise;
      }());

      if (!promiseToWaitFor) {
        // no promise to wait for? okay. Then there's no loader registered
        // nor is a one pending for language that comes from storage.
        // We can just translate.
        determineTranslation(translationId, interpolateParams, interpolationId, defaultTranslationText, uses).then(deferred.resolve, deferred.reject);
      } else {
        var promiseResolved = function () {
          // $uses may have changed while waiting
          if (!forceLanguage) {
            uses = $uses;
          }
          determineTranslation(translationId, interpolateParams, interpolationId, defaultTranslationText, uses).then(deferred.resolve, deferred.reject);
        };
        promiseResolved.displayName = 'promiseResolved';

        promiseToWaitFor['finally'](promiseResolved)['catch'](angular.noop); // we don't care about errors here, already handled
      }
      return deferred.promise;
    };

    /**
     * @name applyNotFoundIndicators
     * @private
     *
     * @description
     * Applies not fount indicators to given translation id, if needed.
     * This function gets only executed, if a translation id doesn't exist,
     * which is why a translation id is expected as argument.
     *
     * @param {string} translationId Translation id.
     * @returns {string} Same as given translation id but applied with not found
     * indicators.
     */
    var applyNotFoundIndicators = function (translationId) {
      // applying notFoundIndicators
      if ($notFoundIndicatorLeft) {
        translationId = [$notFoundIndicatorLeft, translationId].join(' ');
      }
      if ($notFoundIndicatorRight) {
        translationId = [translationId, $notFoundIndicatorRight].join(' ');
      }
      return translationId;
    };

    /**
     * @name useLanguage
     * @private
     *
     * @description
     * Makes actual use of a language by setting a given language key as used
     * language and informs registered interpolators to also use the given
     * key as locale.
     *
     * @param {string} key Locale key.
     */
    var useLanguage = function (key) {
      $uses = key;

      // make sure to store new language key before triggering success event
      if ($storageFactory) {
        Storage.put($translate.storageKey(), $uses);
      }

      $rootScope.$emit('$translateChangeSuccess', {language : key});

      // inform default interpolator
      defaultInterpolator.setLocale($uses);

      var eachInterpolator = function (interpolator, id) {
        interpolatorHashMap[id].setLocale($uses);
      };
      eachInterpolator.displayName = 'eachInterpolatorLocaleSetter';

      // inform all others too!
      angular.forEach(interpolatorHashMap, eachInterpolator);
      $rootScope.$emit('$translateChangeEnd', {language : key});
    };

    /**
     * @name loadAsync
     * @private
     *
     * @description
     * Kicks off registered async loader using `$injector` and applies existing
     * loader options. When resolved, it updates translation tables accordingly
     * or rejects with given language key.
     *
     * @param {string} key Language key.
     * @return {Promise} A promise.
     */
    var loadAsync = function (key) {
      if (!key) {
        throw 'No language key specified for loading.';
      }

      var deferred = $q.defer();

      $rootScope.$emit('$translateLoadingStart', {language : key});
      pendingLoader = true;

      var cache = loaderCache;
      if (typeof(cache) === 'string') {
        // getting on-demand instance of loader
        cache = $injector.get(cache);
      }

      var loaderOptions = angular.extend({}, $loaderOptions, {
        key : key,
        $http : angular.extend({}, {
          cache : cache
        }, $loaderOptions.$http)
      });

      var onLoaderSuccess = function (data) {
        var translationTable = {};
        $rootScope.$emit('$translateLoadingSuccess', {language : key});

        if (angular.isArray(data)) {
          angular.forEach(data, function (table) {
            angular.extend(translationTable, flatObject(table));
          });
        } else {
          angular.extend(translationTable, flatObject(data));
        }
        pendingLoader = false;
        deferred.resolve({
          key : key,
          table : translationTable
        });
        $rootScope.$emit('$translateLoadingEnd', {language : key});
      };
      onLoaderSuccess.displayName = 'onLoaderSuccess';

      var onLoaderError = function (key) {
        $rootScope.$emit('$translateLoadingError', {language : key});
        deferred.reject(key);
        $rootScope.$emit('$translateLoadingEnd', {language : key});
      };
      onLoaderError.displayName = 'onLoaderError';

      $injector.get($loaderFactory)(loaderOptions)
        .then(onLoaderSuccess, onLoaderError);

      return deferred.promise;
    };

    if ($storageFactory) {
      Storage = $injector.get($storageFactory);

      if (!Storage.get || !Storage.put) {
        throw new Error('Couldn\'t use storage \'' + $storageFactory + '\', missing get() or put() method!');
      }
    }

    // if we have additional interpolations that were added via
    // $translateProvider.addInterpolation(), we have to map'em
    if ($interpolatorFactories.length) {
      var eachInterpolationFactory = function (interpolatorFactory) {
        var interpolator = $injector.get(interpolatorFactory);
        // setting initial locale for each interpolation service
        interpolator.setLocale($preferredLanguage || $uses);
        // make'em recognizable through id
        interpolatorHashMap[interpolator.getInterpolationIdentifier()] = interpolator;
      };
      eachInterpolationFactory.displayName = 'interpolationFactoryAdder';

      angular.forEach($interpolatorFactories, eachInterpolationFactory);
    }

    /**
     * @name getTranslationTable
     * @private
     *
     * @description
     * Returns a promise that resolves to the translation table
     * or is rejected if an error occurred.
     *
     * @param langKey
     * @returns {Q.promise}
     */
    var getTranslationTable = function (langKey) {
      var deferred = $q.defer();
      if (Object.prototype.hasOwnProperty.call($translationTable, langKey)) {
        deferred.resolve($translationTable[langKey]);
      } else if (langPromises[langKey]) {
        var onResolve = function (data) {
          translations(data.key, data.table);
          deferred.resolve(data.table);
        };
        onResolve.displayName = 'translationTableResolver';
        langPromises[langKey].then(onResolve, deferred.reject);
      } else {
        deferred.reject();
      }
      return deferred.promise;
    };

    /**
     * @name getFallbackTranslation
     * @private
     *
     * @description
     * Returns a promise that will resolve to the translation
     * or be rejected if no translation was found for the language.
     * This function is currently only used for fallback language translation.
     *
     * @param langKey The language to translate to.
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param sanitizeStrategy
     * @returns {Q.promise}
     */
    var getFallbackTranslation = function (langKey, translationId, interpolateParams, Interpolator, sanitizeStrategy) {
      var deferred = $q.defer();

      var onResolve = function (translationTable) {
        if (Object.prototype.hasOwnProperty.call(translationTable, translationId) && translationTable[translationId] !== null) {
          Interpolator.setLocale(langKey);
          var translation = translationTable[translationId];
          if (translation.substr(0, 2) === '@:') {
            getFallbackTranslation(langKey, translation.substr(2), interpolateParams, Interpolator, sanitizeStrategy)
              .then(deferred.resolve, deferred.reject);
          } else {
            var interpolatedValue = Interpolator.interpolate(translationTable[translationId], interpolateParams, 'service', sanitizeStrategy, translationId);
            interpolatedValue = applyPostProcessing(translationId, translationTable[translationId], interpolatedValue, interpolateParams, langKey);

            deferred.resolve(interpolatedValue);

          }
          Interpolator.setLocale($uses);
        } else {
          deferred.reject();
        }
      };
      onResolve.displayName = 'fallbackTranslationResolver';

      getTranslationTable(langKey).then(onResolve, deferred.reject);

      return deferred.promise;
    };

    /**
     * @name getFallbackTranslationInstant
     * @private
     *
     * @description
     * Returns a translation
     * This function is currently only used for fallback language translation.
     *
     * @param langKey The language to translate to.
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param sanitizeStrategy sanitize strategy override
     *
     * @returns {string} translation
     */
    var getFallbackTranslationInstant = function (langKey, translationId, interpolateParams, Interpolator, sanitizeStrategy) {
      var result, translationTable = $translationTable[langKey];

      if (translationTable && Object.prototype.hasOwnProperty.call(translationTable, translationId) && translationTable[translationId] !== null) {
        Interpolator.setLocale(langKey);
        result = Interpolator.interpolate(translationTable[translationId], interpolateParams, 'filter', sanitizeStrategy, translationId);
        result = applyPostProcessing(translationId, translationTable[translationId], result, interpolateParams, langKey, sanitizeStrategy);
        // workaround for TrustedValueHolderType
        if (!angular.isString(result) && angular.isFunction(result.$$unwrapTrustedValue)) {
          var result2 = result.$$unwrapTrustedValue();
          if (result2.substr(0, 2) === '@:') {
            return getFallbackTranslationInstant(langKey, result2.substr(2), interpolateParams, Interpolator, sanitizeStrategy);
          }
        } else if (result.substr(0, 2) === '@:') {
          return getFallbackTranslationInstant(langKey, result.substr(2), interpolateParams, Interpolator, sanitizeStrategy);
        }
        Interpolator.setLocale($uses);
      }

      return result;
    };


    /**
     * @name translateByHandler
     * @private
     *
     * Translate by missing translation handler.
     *
     * @param translationId
     * @param interpolateParams
     * @param defaultTranslationText
     * @param sanitizeStrategy sanitize strategy override
     *
     * @returns translation created by $missingTranslationHandler or translationId is $missingTranslationHandler is
     * absent
     */
    var translateByHandler = function (translationId, interpolateParams, defaultTranslationText, sanitizeStrategy) {
      // If we have a handler factory - we might also call it here to determine if it provides
      // a default text for a translationid that can't be found anywhere in our tables
      if ($missingTranslationHandlerFactory) {
        return $injector.get($missingTranslationHandlerFactory)(translationId, $uses, interpolateParams, defaultTranslationText, sanitizeStrategy);
      } else {
        return translationId;
      }
    };

    /**
     * @name resolveForFallbackLanguage
     * @private
     *
     * Recursive helper function for fallbackTranslation that will sequentially look
     * for a translation in the fallbackLanguages starting with fallbackLanguageIndex.
     *
     * @param fallbackLanguageIndex
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param defaultTranslationText
     * @param sanitizeStrategy
     * @returns {Q.promise} Promise that will resolve to the translation.
     */
    var resolveForFallbackLanguage = function (fallbackLanguageIndex, translationId, interpolateParams, Interpolator, defaultTranslationText, sanitizeStrategy) {
      var deferred = $q.defer();

      if (fallbackLanguageIndex < $fallbackLanguage.length) {
        var langKey = $fallbackLanguage[fallbackLanguageIndex];
        getFallbackTranslation(langKey, translationId, interpolateParams, Interpolator, sanitizeStrategy).then(
          function (data) {
            deferred.resolve(data);
          },
          function () {
            // Look in the next fallback language for a translation.
            // It delays the resolving by passing another promise to resolve.
            return resolveForFallbackLanguage(fallbackLanguageIndex + 1, translationId, interpolateParams, Interpolator, defaultTranslationText, sanitizeStrategy).then(deferred.resolve, deferred.reject);
          }
        );
      } else {
        // No translation found in any fallback language
        // if a default translation text is set in the directive, then return this as a result
        if (defaultTranslationText) {
          deferred.resolve(defaultTranslationText);
        } else {
          var missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams, defaultTranslationText);

          // if no default translation is set and an error handler is defined, send it to the handler
          // and then return the result if it isn't undefined
          if ($missingTranslationHandlerFactory && missingTranslationHandlerTranslation) {
            deferred.resolve(missingTranslationHandlerTranslation);
          } else {
            deferred.reject(applyNotFoundIndicators(translationId));
          }
        }
      }
      return deferred.promise;
    };

    /**
     * @name resolveForFallbackLanguageInstant
     * @private
     *
     * Recursive helper function for fallbackTranslation that will sequentially look
     * for a translation in the fallbackLanguages starting with fallbackLanguageIndex.
     *
     * @param fallbackLanguageIndex
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param sanitizeStrategy
     * @returns {string} translation
     */
    var resolveForFallbackLanguageInstant = function (fallbackLanguageIndex, translationId, interpolateParams, Interpolator, sanitizeStrategy) {
      var result;

      if (fallbackLanguageIndex < $fallbackLanguage.length) {
        var langKey = $fallbackLanguage[fallbackLanguageIndex];
        result = getFallbackTranslationInstant(langKey, translationId, interpolateParams, Interpolator, sanitizeStrategy);
        if (!result && result !== '') {
          result = resolveForFallbackLanguageInstant(fallbackLanguageIndex + 1, translationId, interpolateParams, Interpolator);
        }
      }
      return result;
    };

    /**
     * Translates with the usage of the fallback languages.
     *
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param defaultTranslationText
     * @param sanitizeStrategy
     * @returns {Q.promise} Promise, that resolves to the translation.
     */
    var fallbackTranslation = function (translationId, interpolateParams, Interpolator, defaultTranslationText, sanitizeStrategy) {
      // Start with the fallbackLanguage with index 0
      return resolveForFallbackLanguage((startFallbackIteration > 0 ? startFallbackIteration : fallbackIndex), translationId, interpolateParams, Interpolator, defaultTranslationText, sanitizeStrategy);
    };

    /**
     * Translates with the usage of the fallback languages.
     *
     * @param translationId
     * @param interpolateParams
     * @param Interpolator
     * @param sanitizeStrategy
     * @returns {String} translation
     */
    var fallbackTranslationInstant = function (translationId, interpolateParams, Interpolator, sanitizeStrategy) {
      // Start with the fallbackLanguage with index 0
      return resolveForFallbackLanguageInstant((startFallbackIteration > 0 ? startFallbackIteration : fallbackIndex), translationId, interpolateParams, Interpolator, sanitizeStrategy);
    };

    var determineTranslation = function (translationId, interpolateParams, interpolationId, defaultTranslationText, uses, sanitizeStrategy) {

      var deferred = $q.defer();

      var table = uses ? $translationTable[uses] : $translationTable,
        Interpolator = (interpolationId) ? interpolatorHashMap[interpolationId] : defaultInterpolator;

      // if the translation id exists, we can just interpolate it
      if (table && Object.prototype.hasOwnProperty.call(table, translationId) && table[translationId] !== null) {
        var translation = table[translationId];

        // If using link, rerun $translate with linked translationId and return it
        if (translation.substr(0, 2) === '@:') {

          $translate(translation.substr(2), interpolateParams, interpolationId, defaultTranslationText, uses)
            .then(deferred.resolve, deferred.reject);
        } else {
          //
          var resolvedTranslation = Interpolator.interpolate(translation, interpolateParams, 'service', sanitizeStrategy, translationId);
          resolvedTranslation = applyPostProcessing(translationId, translation, resolvedTranslation, interpolateParams, uses);
          deferred.resolve(resolvedTranslation);
        }
      } else {
        var missingTranslationHandlerTranslation;
        // for logging purposes only (as in $translateMissingTranslationHandlerLog), value is not returned to promise
        if ($missingTranslationHandlerFactory && !pendingLoader) {
          missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams, defaultTranslationText);
        }

        // since we couldn't translate the inital requested translation id,
        // we try it now with one or more fallback languages, if fallback language(s) is
        // configured.
        if (uses && $fallbackLanguage && $fallbackLanguage.length) {
          fallbackTranslation(translationId, interpolateParams, Interpolator, defaultTranslationText, sanitizeStrategy)
            .then(function (translation) {
              deferred.resolve(translation);
            }, function (_translationId) {
              deferred.reject(applyNotFoundIndicators(_translationId));
            });
        } else if ($missingTranslationHandlerFactory && !pendingLoader && missingTranslationHandlerTranslation) {
          // looks like the requested translation id doesn't exists.
          // Now, if there is a registered handler for missing translations and no
          // asyncLoader is pending, we execute the handler
          if (defaultTranslationText) {
            deferred.resolve(defaultTranslationText);
          } else {
            deferred.resolve(missingTranslationHandlerTranslation);
          }
        } else {
          if (defaultTranslationText) {
            deferred.resolve(defaultTranslationText);
          } else {
            deferred.reject(applyNotFoundIndicators(translationId));
          }
        }
      }
      return deferred.promise;
    };

    var determineTranslationInstant = function (translationId, interpolateParams, interpolationId, uses, sanitizeStrategy) {

      var result, table = uses ? $translationTable[uses] : $translationTable,
        Interpolator = defaultInterpolator;

      // if the interpolation id exists use custom interpolator
      if (interpolatorHashMap && Object.prototype.hasOwnProperty.call(interpolatorHashMap, interpolationId)) {
        Interpolator = interpolatorHashMap[interpolationId];
      }

      // if the translation id exists, we can just interpolate it
      if (table && Object.prototype.hasOwnProperty.call(table, translationId) && table[translationId] !== null) {
        var translation = table[translationId];

        // If using link, rerun $translate with linked translationId and return it
        if (translation.substr(0, 2) === '@:') {
          result = determineTranslationInstant(translation.substr(2), interpolateParams, interpolationId, uses, sanitizeStrategy);
        } else {
          result = Interpolator.interpolate(translation, interpolateParams, 'filter', sanitizeStrategy, translationId);
          result = applyPostProcessing(translationId, translation, result, interpolateParams, uses, sanitizeStrategy);
        }
      } else {
        var missingTranslationHandlerTranslation;
        // for logging purposes only (as in $translateMissingTranslationHandlerLog), value is not returned to promise
        if ($missingTranslationHandlerFactory && !pendingLoader) {
          missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams, sanitizeStrategy);
        }

        // since we couldn't translate the inital requested translation id,
        // we try it now with one or more fallback languages, if fallback language(s) is
        // configured.
        if (uses && $fallbackLanguage && $fallbackLanguage.length) {
          fallbackIndex = 0;
          result = fallbackTranslationInstant(translationId, interpolateParams, Interpolator, sanitizeStrategy);
        } else if ($missingTranslationHandlerFactory && !pendingLoader && missingTranslationHandlerTranslation) {
          // looks like the requested translation id doesn't exists.
          // Now, if there is a registered handler for missing translations and no
          // asyncLoader is pending, we execute the handler
          result = missingTranslationHandlerTranslation;
        } else {
          result = applyNotFoundIndicators(translationId);
        }
      }

      return result;
    };

    var clearNextLangAndPromise = function (key) {
      if ($nextLang === key) {
        $nextLang = undefined;
      }
      langPromises[key] = undefined;
    };

    var applyPostProcessing = function (translationId, translation, resolvedTranslation, interpolateParams, uses, sanitizeStrategy) {
      var fn = postProcessFn;

      if (fn) {

        if (typeof(fn) === 'string') {
          // getting on-demand instance
          fn = $injector.get(fn);
        }
        if (fn) {
          return fn(translationId, translation, resolvedTranslation, interpolateParams, uses, sanitizeStrategy);
        }
      }

      return resolvedTranslation;
    };

    var loadTranslationsIfMissing = function (key) {
      if (!$translationTable[key] && $loaderFactory && !langPromises[key]) {
        langPromises[key] = loadAsync(key).then(function (translation) {
          translations(translation.key, translation.table);
          return translation;
        });
      }
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#preferredLanguage
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the language key for the preferred language.
     *
     * @param {string} langKey language String or Array to be used as preferredLanguage (changing at runtime)
     *
     * @return {string} preferred language key
     */
    $translate.preferredLanguage = function (langKey) {
      if (langKey) {
        setupPreferredLanguage(langKey);
      }
      return $preferredLanguage;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#cloakClassName
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the configured class name for `translate-cloak` directive.
     *
     * @return {string} cloakClassName
     */
    $translate.cloakClassName = function () {
      return $cloakClassName;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#nestedObjectDelimeter
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the configured delimiter for nested namespaces.
     *
     * @return {string} nestedObjectDelimeter
     */
    $translate.nestedObjectDelimeter = function () {
      return $nestedObjectDelimeter;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#fallbackLanguage
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the language key for the fallback languages or sets a new fallback stack.
     *
     * @param {string=} langKey language String or Array of fallback languages to be used (to change stack at runtime)
     *
     * @return {string||array} fallback language key
     */
    $translate.fallbackLanguage = function (langKey) {
      if (langKey !== undefined && langKey !== null) {
        fallbackStack(langKey);

        // as we might have an async loader initiated and a new translation language might have been defined
        // we need to add the promise to the stack also. So - iterate.
        if ($loaderFactory) {
          if ($fallbackLanguage && $fallbackLanguage.length) {
            for (var i = 0, len = $fallbackLanguage.length; i < len; i++) {
              if (!langPromises[$fallbackLanguage[i]]) {
                langPromises[$fallbackLanguage[i]] = loadAsync($fallbackLanguage[i]);
              }
            }
          }
        }
        $translate.use($translate.use());
      }
      if ($fallbackWasString) {
        return $fallbackLanguage[0];
      } else {
        return $fallbackLanguage;
      }

    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#useFallbackLanguage
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Sets the first key of the fallback language stack to be used for translation.
     * Therefore all languages in the fallback array BEFORE this key will be skipped!
     *
     * @param {string=} langKey Contains the langKey the iteration shall start with. Set to false if you want to
     * get back to the whole stack
     */
    $translate.useFallbackLanguage = function (langKey) {
      if (langKey !== undefined && langKey !== null) {
        if (!langKey) {
          startFallbackIteration = 0;
        } else {
          var langKeyPosition = indexOf($fallbackLanguage, langKey);
          if (langKeyPosition > -1) {
            startFallbackIteration = langKeyPosition;
          }
        }

      }

    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#proposedLanguage
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the language key of language that is currently loaded asynchronously.
     *
     * @return {string} language key
     */
    $translate.proposedLanguage = function () {
      return $nextLang;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#storage
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns registered storage.
     *
     * @return {object} Storage
     */
    $translate.storage = function () {
      return Storage;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#negotiateLocale
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns a language key based on available languages and language aliases. If a
     * language key cannot be resolved, returns undefined.
     *
     * If no or a falsy key is given, returns undefined.
     *
     * @param {string} [key] Language key
     * @return {string|undefined} Language key or undefined if no language key is found.
     */
    $translate.negotiateLocale = negotiateLocale;

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#use
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Tells angular-translate which language to use by given language key. This method is
     * used to change language at runtime. It also takes care of storing the language
     * key in a configured store to let your app remember the choosed language.
     *
     * When trying to 'use' a language which isn't available it tries to load it
     * asynchronously with registered loaders.
     *
     * Returns promise object with loaded language file data or string of the currently used language.
     *
     * If no or a falsy key is given it returns the currently used language key.
     * The returned string will be ```undefined``` if setting up $translate hasn't finished.
     * @example
     * $translate.use("en_US").then(function(data){
       *   $scope.text = $translate("HELLO");
       * });
     *
     * @param {string} [key] Language key
     * @return {object|string} Promise with loaded language data or the language key if a falsy param was given.
     */
    $translate.use = function (key) {
      if (!key) {
        return $uses;
      }

      var deferred = $q.defer();
      deferred.promise.then(null, angular.noop); // AJS "Possibly unhandled rejection"

      $rootScope.$emit('$translateChangeStart', {language : key});

      // Try to get the aliased language key
      var aliasedKey = negotiateLocale(key);
      // Ensure only registered language keys will be loaded
      if ($availableLanguageKeys.length > 0 && !aliasedKey) {
        return $q.reject(key);
      }

      if (aliasedKey) {
        key = aliasedKey;
      }

      // if there isn't a translation table for the language we've requested,
      // we load it asynchronously
      $nextLang = key;
      if (($forceAsyncReloadEnabled || !$translationTable[key]) && $loaderFactory && !langPromises[key]) {
        langPromises[key] = loadAsync(key).then(function (translation) {
          translations(translation.key, translation.table);
          deferred.resolve(translation.key);
          if ($nextLang === key) {
            useLanguage(translation.key);
          }
          return translation;
        }, function (key) {
          $rootScope.$emit('$translateChangeError', {language : key});
          deferred.reject(key);
          $rootScope.$emit('$translateChangeEnd', {language : key});
          return $q.reject(key);
        });
        langPromises[key]['finally'](function () {
          clearNextLangAndPromise(key);
        })['catch'](angular.noop); // we don't care about errors (clearing)
      } else if (langPromises[key]) {
        // we are already loading this asynchronously
        // resolve our new deferred when the old langPromise is resolved
        langPromises[key].then(function (translation) {
          if ($nextLang === translation.key) {
            useLanguage(translation.key);
          }
          deferred.resolve(translation.key);
          return translation;
        }, function (key) {
          // find first available fallback language if that request has failed
          if (!$uses && $fallbackLanguage && $fallbackLanguage.length > 0 && $fallbackLanguage[0] !== key) {
            return $translate.use($fallbackLanguage[0]).then(deferred.resolve, deferred.reject);
          } else {
            return deferred.reject(key);
          }
        });
      } else {
        deferred.resolve(key);
        useLanguage(key);
      }

      return deferred.promise;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#resolveClientLocale
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * This returns the current browser/client's language key. The result is processed with the configured uniform tag resolver.
     *
     * @returns {string} the current client/browser language key
     */
    $translate.resolveClientLocale = function () {
      return getLocale();
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#storageKey
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the key for the storage.
     *
     * @return {string} storage key
     */
    $translate.storageKey = function () {
      return storageKey();
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#isPostCompilingEnabled
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns whether post compiling is enabled or not
     *
     * @return {bool} storage key
     */
    $translate.isPostCompilingEnabled = function () {
      return $postCompilingEnabled;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#isForceAsyncReloadEnabled
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns whether force async reload is enabled or not
     *
     * @return {boolean} forceAsyncReload value
     */
    $translate.isForceAsyncReloadEnabled = function () {
      return $forceAsyncReloadEnabled;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#isKeepContent
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns whether keepContent or not
     *
     * @return {boolean} keepContent value
     */
    $translate.isKeepContent = function () {
      return $keepContent;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#refresh
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Refreshes a translation table pointed by the given langKey. If langKey is not specified,
     * the module will drop all existent translation tables and load new version of those which
     * are currently in use.
     *
     * Refresh means that the module will drop target translation table and try to load it again.
     *
     * In case there are no loaders registered the refresh() method will throw an Error.
     *
     * If the module is able to refresh translation tables refresh() method will broadcast
     * $translateRefreshStart and $translateRefreshEnd events.
     *
     * @example
     * // this will drop all currently existent translation tables and reload those which are
     * // currently in use
     * $translate.refresh();
     * // this will refresh a translation table for the en_US language
     * $translate.refresh('en_US');
     *
     * @param {string} langKey A language key of the table, which has to be refreshed
     *
     * @return {promise} Promise, which will be resolved in case a translation tables refreshing
     * process is finished successfully, and reject if not.
     */
    $translate.refresh = function (langKey) {
      if (!$loaderFactory) {
        throw new Error('Couldn\'t refresh translation table, no loader registered!');
      }

      $rootScope.$emit('$translateRefreshStart', {language : langKey});

      var deferred = $q.defer(), updatedLanguages = {};

      //private helper
      function loadNewData(languageKey) {
        var promise = loadAsync(languageKey);
        //update the load promise cache for this language
        langPromises[languageKey] = promise;
        //register a data handler for the promise
        promise.then(function (data) {
            //clear the cache for this language
            $translationTable[languageKey] = {};
            //add the new data for this language
            translations(languageKey, data.table);
            //track that we updated this language
            updatedLanguages[languageKey] = true;
          },
          //handle rejection to appease the $q validation
          angular.noop);
        return promise;
      }

      //set up post-processing
      deferred.promise.then(
        function () {
          for (var key in $translationTable) {
            if ($translationTable.hasOwnProperty(key)) {
              //delete cache entries that were not updated
              if (!(key in updatedLanguages)) {
                delete $translationTable[key];
              }
            }
          }
          if ($uses) {
            useLanguage($uses);
          }
        },
        //handle rejection to appease the $q validation
        angular.noop
      )['finally'](
        function () {
          $rootScope.$emit('$translateRefreshEnd', {language : langKey});
        }
      );

      if (!langKey) {
        // if there's no language key specified we refresh ALL THE THINGS!
        var languagesToReload = $fallbackLanguage && $fallbackLanguage.slice() || [];
        if ($uses && languagesToReload.indexOf($uses) === -1) {
          languagesToReload.push($uses);
        }
        $q.all(languagesToReload.map(loadNewData)).then(deferred.resolve, deferred.reject);

      } else if ($translationTable[langKey]) {
        //just refresh the specified language cache
        loadNewData(langKey).then(deferred.resolve, deferred.reject);

      } else {
        deferred.reject();
      }

      return deferred.promise;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#instant
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns a translation instantly from the internal state of loaded translation. All rules
     * regarding the current language, the preferred language of even fallback languages will be
     * used except any promise handling. If a language was not found, an asynchronous loading
     * will be invoked in the background.
     *
     * @param {string|array} translationId A token which represents a translation id
     *                                     This can be optionally an array of translation ids which
     *                                     results that the function's promise returns an object where
     *                                     each key is the translation id and the value the translation.
     * @param {object} interpolateParams Params
     * @param {string} interpolationId The id of the interpolation to use
     * @param {string} forceLanguage A language to be used instead of the current language
     * @param {string} sanitizeStrategy force sanitize strategy for this call instead of using the configured one
     *
     * @return {string|object} translation
     */
    $translate.instant = function (translationId, interpolateParams, interpolationId, forceLanguage, sanitizeStrategy) {

      // we don't want to re-negotiate $uses
      var uses = (forceLanguage && forceLanguage !== $uses) ? // we don't want to re-negotiate $uses
        (negotiateLocale(forceLanguage) || forceLanguage) : $uses;

      // Detect undefined and null values to shorten the execution and prevent exceptions
      if (translationId === null || angular.isUndefined(translationId)) {
        return translationId;
      }

      // Check forceLanguage is present
      if (forceLanguage) {
        loadTranslationsIfMissing(forceLanguage);
      }

      // Duck detection: If the first argument is an array, a bunch of translations was requested.
      // The result is an object.
      if (angular.isArray(translationId)) {
        var results = {};
        for (var i = 0, c = translationId.length; i < c; i++) {
          results[translationId[i]] = $translate.instant(translationId[i], interpolateParams, interpolationId, forceLanguage, sanitizeStrategy);
        }
        return results;
      }

      // We discarded unacceptable values. So we just need to verify if translationId is empty String
      if (angular.isString(translationId) && translationId.length < 1) {
        return translationId;
      }

      // trim off any whitespace
      if (translationId) {
        translationId = trim.apply(translationId);
      }

      var result, possibleLangKeys = [];
      if ($preferredLanguage) {
        possibleLangKeys.push($preferredLanguage);
      }
      if (uses) {
        possibleLangKeys.push(uses);
      }
      if ($fallbackLanguage && $fallbackLanguage.length) {
        possibleLangKeys = possibleLangKeys.concat($fallbackLanguage);
      }
      for (var j = 0, d = possibleLangKeys.length; j < d; j++) {
        var possibleLangKey = possibleLangKeys[j];
        if ($translationTable[possibleLangKey]) {
          if (typeof $translationTable[possibleLangKey][translationId] !== 'undefined') {
            result = determineTranslationInstant(translationId, interpolateParams, interpolationId, uses, sanitizeStrategy);
          }
        }
        if (typeof result !== 'undefined') {
          break;
        }
      }

      if (!result && result !== '') {
        if ($notFoundIndicatorLeft || $notFoundIndicatorRight) {
          result = applyNotFoundIndicators(translationId);
        } else {
          // Return translation of default interpolator if not found anything.
          result = defaultInterpolator.interpolate(translationId, interpolateParams, 'filter', sanitizeStrategy);

          // looks like the requested translation id doesn't exists.
          // Now, if there is a registered handler for missing translations and no
          // asyncLoader is pending, we execute the handler
          var missingTranslationHandlerTranslation;
          if ($missingTranslationHandlerFactory && !pendingLoader) {
            missingTranslationHandlerTranslation = translateByHandler(translationId, interpolateParams, sanitizeStrategy);
          }

          if ($missingTranslationHandlerFactory && !pendingLoader && missingTranslationHandlerTranslation) {
            result = missingTranslationHandlerTranslation;
          }
        }
      }

      return result;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#versionInfo
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the current version information for the angular-translate library
     *
     * @return {string} angular-translate version
     */
    $translate.versionInfo = function () {
      return version;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#loaderCache
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns the defined loaderCache.
     *
     * @return {boolean|string|object} current value of loaderCache
     */
    $translate.loaderCache = function () {
      return loaderCache;
    };

    // internal purpose only
    $translate.directivePriority = function () {
      return directivePriority;
    };

    // internal purpose only
    $translate.statefulFilter = function () {
      return statefulFilter;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#isReady
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns whether the service is "ready" to translate (i.e. loading 1st language).
     *
     * See also {@link pascalprecht.translate.$translate#methods_onReady onReady()}.
     *
     * @return {boolean} current value of ready
     */
    $translate.isReady = function () {
      return $isReady;
    };

    var $onReadyDeferred = $q.defer();
    $onReadyDeferred.promise.then(function () {
      $isReady = true;
    });

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#onReady
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Calls the function provided or resolved the returned promise after the service is "ready" to translate (i.e. loading 1st language).
     *
     * See also {@link pascalprecht.translate.$translate#methods_isReady isReady()}.
     *
     * @param {Function=} fn Function to invoke when service is ready
     * @return {object} Promise resolved when service is ready
     */
    $translate.onReady = function (fn) {
      var deferred = $q.defer();
      if (angular.isFunction(fn)) {
        deferred.promise.then(fn);
      }
      if ($isReady) {
        deferred.resolve();
      } else {
        $onReadyDeferred.promise.then(deferred.resolve);
      }
      return deferred.promise;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#getAvailableLanguageKeys
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * This function simply returns the registered language keys being defined before in the config phase
     * With this, an application can use the array to provide a language selection dropdown or similar
     * without any additional effort
     *
     * @returns {object} returns the list of possibly registered language keys and mapping or null if not defined
     */
    $translate.getAvailableLanguageKeys = function () {
      if ($availableLanguageKeys.length > 0) {
        return $availableLanguageKeys;
      }
      return null;
    };

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translate#getTranslationTable
     * @methodOf pascalprecht.translate.$translate
     *
     * @description
     * Returns translation table by the given language key.
     *
     * Unless a language is provided it returns a translation table of the current one.
     * Note: If translation dictionary is currently downloading or in progress
     * it will return null.
     *
     * @param {string} langKey A token which represents a translation id
     *
     * @return {object} a copy of angular-translate $translationTable
     */
    $translate.getTranslationTable = function (langKey) {
      langKey = langKey || $translate.use();
      if (langKey && $translationTable[langKey]) {
        return angular.copy($translationTable[langKey]);
      }
      return null;
    };

    // Whenever $translateReady is being fired, this will ensure the state of $isReady
    var globalOnReadyListener = $rootScope.$on('$translateReady', function () {
      $onReadyDeferred.resolve();
      globalOnReadyListener(); // one time only
      globalOnReadyListener = null;
    });
    var globalOnChangeListener = $rootScope.$on('$translateChangeEnd', function () {
      $onReadyDeferred.resolve();
      globalOnChangeListener(); // one time only
      globalOnChangeListener = null;
    });

    if ($loaderFactory) {

      // If at least one async loader is defined and there are no
      // (default) translations available we should try to load them.
      if (angular.equals($translationTable, {})) {
        if ($translate.use()) {
          $translate.use($translate.use());
        }
      }

      // Also, if there are any fallback language registered, we start
      // loading them asynchronously as soon as we can.
      if ($fallbackLanguage && $fallbackLanguage.length) {
        var processAsyncResult = function (translation) {
          translations(translation.key, translation.table);
          $rootScope.$emit('$translateChangeEnd', {language : translation.key});
          return translation;
        };
        for (var i = 0, len = $fallbackLanguage.length; i < len; i++) {
          var fallbackLanguageId = $fallbackLanguage[i];
          if ($forceAsyncReloadEnabled || !$translationTable[fallbackLanguageId]) {
            langPromises[fallbackLanguageId] = loadAsync(fallbackLanguageId).then(processAsyncResult);
          }
        }
      }
    } else {
      $rootScope.$emit('$translateReady', {language : $translate.use()});
    }

    return $translate;
  }];
}

$translate.displayName = 'displayName';

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateDefaultInterpolation
 * @requires $interpolate
 *
 * @description
 * Uses angular's `$interpolate` services to interpolate strings against some values.
 *
 * Be aware to configure a proper sanitization strategy.
 *
 * See also:
 * * {@link pascalprecht.translate.$translateSanitization}
 *
 * @return {object} $translateDefaultInterpolation Interpolator service
 */
angular.module('pascalprecht.translate').factory('$translateDefaultInterpolation', $translateDefaultInterpolation);

function $translateDefaultInterpolation ($interpolate, $translateSanitization) {

  'use strict';

  var $translateInterpolator = {},
      $locale,
      $identifier = 'default';

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#setLocale
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Sets current locale (this is currently not use in this interpolation).
   *
   * @param {string} locale Language key or locale.
   */
  $translateInterpolator.setLocale = function (locale) {
    $locale = locale;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#getInterpolationIdentifier
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Returns an identifier for this interpolation service.
   *
   * @returns {string} $identifier
   */
  $translateInterpolator.getInterpolationIdentifier = function () {
    return $identifier;
  };

  /**
   * @deprecated will be removed in 3.0
   * @see {@link pascalprecht.translate.$translateSanitization}
   */
  $translateInterpolator.useSanitizeValueStrategy = function (value) {
    $translateSanitization.useStrategy(value);
    return this;
  };

  /**
   * @ngdoc function
   * @name pascalprecht.translate.$translateDefaultInterpolation#interpolate
   * @methodOf pascalprecht.translate.$translateDefaultInterpolation
   *
   * @description
   * Interpolates given value agains given interpolate params using angulars
   * `$interpolate` service.
   *
   * Since AngularJS 1.5, `value` must not be a string but can be anything input.
   *
   * @param {string} value translation
   * @param {object} interpolationParams interpolation params
   * @param {string} context current context (filter, directive, service)
   * @param {string} sanitizeStrategy sanitize strategy
   * @param {string} translationId current translationId
   *
   * @returns {string} interpolated string
   */
  $translateInterpolator.interpolate = function (value, interpolationParams, context, sanitizeStrategy, translationId) { // jshint ignore:line
    interpolationParams = interpolationParams || {};
    interpolationParams = $translateSanitization.sanitize(interpolationParams, 'params', sanitizeStrategy, context);

    var interpolatedText;
    if (angular.isNumber(value)) {
      // numbers are safe
      interpolatedText = '' + value;
    } else if (angular.isString(value)) {
      // strings must be interpolated (that's the job here)
      interpolatedText = $interpolate(value)(interpolationParams);
      interpolatedText = $translateSanitization.sanitize(interpolatedText, 'text', sanitizeStrategy, context);
    } else {
      // neither a number or a string, cant interpolate => empty string
      interpolatedText = '';
    }

    return interpolatedText;
  };

  return $translateInterpolator;
}

$translateDefaultInterpolation.displayName = '$translateDefaultInterpolation';

angular.module('pascalprecht.translate').constant('$STORAGE_KEY', 'NG_TRANSLATE_LANG_KEY');

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translate
 * @requires $interpolate, 
 * @requires $compile, 
 * @requires $parse, 
 * @requires $rootScope
 * @restrict AE
 *
 * @description
 * Translates given translation id either through attribute or DOM content.
 * Internally it uses $translate service to translate the translation id. It possible to
 * pass an optional `translate-values` object literal as string into translation id.
 *
 * @param {string=} translate Translation id which could be either string or interpolated string.
 * @param {string=} translate-values Values to pass into translation id. Can be passed as object literal string or interpolated object.
 * @param {string=} translate-attr-ATTR translate Translation id and put it into ATTR attribute.
 * @param {string=} translate-default will be used unless translation was successful
 * @param {boolean=} translate-compile (default true if present) defines locally activation of {@link pascalprecht.translate.$translateProvider#methods_usePostCompiling}
 * @param {boolean=} translate-keep-content (default true if present) defines that in case a KEY could not be translated, that the existing content is left in the innerHTML}
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div ng-controller="TranslateCtrl">

        <pre translate="TRANSLATION_ID"></pre>
        <pre translate>TRANSLATION_ID</pre>
        <pre translate translate-attr-title="TRANSLATION_ID"></pre>
        <pre translate="{{translationId}}"></pre>
        <pre translate>{{translationId}}</pre>
        <pre translate="WITH_VALUES" translate-values="{value: 5}"></pre>
        <pre translate translate-values="{value: 5}">WITH_VALUES</pre>
        <pre translate="WITH_VALUES" translate-values="{{values}}"></pre>
        <pre translate translate-values="{{values}}">WITH_VALUES</pre>
        <pre translate translate-attr-title="WITH_VALUES" translate-values="{{values}}"></pre>
        <pre translate="WITH_CAMEL_CASE_KEY" translate-value-camel-case-key="Hi"></pre>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en',{
          'TRANSLATION_ID': 'Hello there!',
          'WITH_VALUES': 'The following value is dynamic: {{value}}',
          'WITH_CAMEL_CASE_KEY': 'The interpolation key is camel cased: {{camelCaseKey}}'
        }).preferredLanguage('en');

      });

      angular.module('ngView').controller('TranslateCtrl', function ($scope) {
        $scope.translationId = 'TRANSLATION_ID';

        $scope.values = {
          value: 78
        };
      });
    </file>
    <file name="scenario.js">
      it('should translate', function () {
        inject(function ($rootScope, $compile) {
          $rootScope.translationId = 'TRANSLATION_ID';

          element = $compile('<p translate="TRANSLATION_ID"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate="{{translationId}}"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate>TRANSLATION_ID</p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate>{{translationId}}</p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('Hello there!');

          element = $compile('<p translate translate-attr-title="TRANSLATION_ID"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.attr('title')).toBe('Hello there!');

          element = $compile('<p translate="WITH_CAMEL_CASE_KEY" translate-value-camel-case-key="Hello"></p>')($rootScope);
          $rootScope.$digest();
          expect(element.text()).toBe('The interpolation key is camel cased: Hello');
        });
      });
    </file>
   </example>
 */
.directive('translate', translateDirective);
function translateDirective($translate, $interpolate, $compile, $parse, $rootScope) {

  'use strict';

  /**
   * @name trim
   * @private
   *
   * @description
   * trim polyfill
   *
   * @returns {string} The string stripped of whitespace from both ends
   */
  var trim = function() {
    return this.toString().replace(/^\s+|\s+$/g, '');
  };

  return {
    restrict: 'AE',
    scope: true,
    priority: $translate.directivePriority(),
    compile: function (tElement, tAttr) {

      var translateValuesExist = (tAttr.translateValues) ?
        tAttr.translateValues : undefined;

      var translateInterpolation = (tAttr.translateInterpolation) ?
        tAttr.translateInterpolation : undefined;

      var translateValueExist = tElement[0].outerHTML.match(/translate-value-+/i);

      var interpolateRegExp = '^(.*)(' + $interpolate.startSymbol() + '.*' + $interpolate.endSymbol() + ')(.*)',
          watcherRegExp = '^(.*)' + $interpolate.startSymbol() + '(.*)' + $interpolate.endSymbol() + '(.*)';

      return function linkFn(scope, iElement, iAttr) {

        scope.interpolateParams = {};
        scope.preText = '';
        scope.postText = '';
        scope.translateNamespace = getTranslateNamespace(scope);
        var translationIds = {};

        var initInterpolationParams = function (interpolateParams, iAttr, tAttr) {
          // initial setup
          if (iAttr.translateValues) {
            angular.extend(interpolateParams, $parse(iAttr.translateValues)(scope.$parent));
          }
          // initially fetch all attributes if existing and fill the params
          if (translateValueExist) {
            for (var attr in tAttr) {
              if (Object.prototype.hasOwnProperty.call(iAttr, attr) && attr.substr(0, 14) === 'translateValue' && attr !== 'translateValues') {
                var attributeName = angular.lowercase(attr.substr(14, 1)) + attr.substr(15);
                interpolateParams[attributeName] = tAttr[attr];
              }
            }
          }
        };

        // Ensures any change of the attribute "translate" containing the id will
        // be re-stored to the scope's "translationId".
        // If the attribute has no content, the element's text value (white spaces trimmed off) will be used.
        var observeElementTranslation = function (translationId) {

          // Remove any old watcher
          if (angular.isFunction(observeElementTranslation._unwatchOld)) {
            observeElementTranslation._unwatchOld();
            observeElementTranslation._unwatchOld = undefined;
          }

          if (angular.equals(translationId , '') || !angular.isDefined(translationId)) {
            var iElementText = trim.apply(iElement.text());

            // Resolve translation id by inner html if required
            var interpolateMatches = iElementText.match(interpolateRegExp);
            // Interpolate translation id if required
            if (angular.isArray(interpolateMatches)) {
              scope.preText = interpolateMatches[1];
              scope.postText = interpolateMatches[3];
              translationIds.translate = $interpolate(interpolateMatches[2])(scope.$parent);
              var watcherMatches = iElementText.match(watcherRegExp);
              if (angular.isArray(watcherMatches) && watcherMatches[2] && watcherMatches[2].length) {
                observeElementTranslation._unwatchOld = scope.$watch(watcherMatches[2], function (newValue) {
                  translationIds.translate = newValue;
                  updateTranslations();
                });
              }
            } else {
              // do not assigne the translation id if it is empty.
              translationIds.translate = !iElementText ? undefined : iElementText;
            }
          } else {
            translationIds.translate = translationId;
          }
          updateTranslations();
        };

        var observeAttributeTranslation = function (translateAttr) {
          iAttr.$observe(translateAttr, function (translationId) {
            translationIds[translateAttr] = translationId;
            updateTranslations();
          });
        };

        // initial setup with values
        initInterpolationParams(scope.interpolateParams, iAttr, tAttr);

        var firstAttributeChangedEvent = true;
        iAttr.$observe('translate', function (translationId) {
          if (typeof translationId === 'undefined') {
            // case of element "<translate>xyz</translate>"
            observeElementTranslation('');
          } else {
            // case of regular attribute
            if (translationId !== '' || !firstAttributeChangedEvent) {
              translationIds.translate = translationId;
              updateTranslations();
            }
          }
          firstAttributeChangedEvent = false;
        });

        for (var translateAttr in iAttr) {
          if (iAttr.hasOwnProperty(translateAttr) && translateAttr.substr(0, 13) === 'translateAttr' && translateAttr.length > 13) {
            observeAttributeTranslation(translateAttr);
          }
        }

        iAttr.$observe('translateDefault', function (value) {
          scope.defaultText = value;
          updateTranslations();
        });

        if (translateValuesExist) {
          iAttr.$observe('translateValues', function (interpolateParams) {
            if (interpolateParams) {
              scope.$parent.$watch(function () {
                angular.extend(scope.interpolateParams, $parse(interpolateParams)(scope.$parent));
              });
            }
          });
        }

        if (translateValueExist) {
          var observeValueAttribute = function (attrName) {
            iAttr.$observe(attrName, function (value) {
              var attributeName = angular.lowercase(attrName.substr(14, 1)) + attrName.substr(15);
              scope.interpolateParams[attributeName] = value;
            });
          };
          for (var attr in iAttr) {
            if (Object.prototype.hasOwnProperty.call(iAttr, attr) && attr.substr(0, 14) === 'translateValue' && attr !== 'translateValues') {
              observeValueAttribute(attr);
            }
          }
        }

        // Master update function
        var updateTranslations = function () {
          for (var key in translationIds) {
            if (translationIds.hasOwnProperty(key) && translationIds[key] !== undefined) {
              updateTranslation(key, translationIds[key], scope, scope.interpolateParams, scope.defaultText, scope.translateNamespace);
            }
          }
        };

        // Put translation processing function outside loop
        var updateTranslation = function(translateAttr, translationId, scope, interpolateParams, defaultTranslationText, translateNamespace) {
          if (translationId) {
            // if translation id starts with '.' and translateNamespace given, prepend namespace
            if (translateNamespace && translationId.charAt(0) === '.') {
              translationId = translateNamespace + translationId;
            }

            $translate(translationId, interpolateParams, translateInterpolation, defaultTranslationText, scope.translateLanguage)
              .then(function (translation) {
                applyTranslation(translation, scope, true, translateAttr);
              }, function (translationId) {
                applyTranslation(translationId, scope, false, translateAttr);
              });
          } else {
            // as an empty string cannot be translated, we can solve this using successful=false
            applyTranslation(translationId, scope, false, translateAttr);
          }
        };

        var applyTranslation = function (value, scope, successful, translateAttr) {
          if (!successful) {
            if (typeof scope.defaultText !== 'undefined') {
              value = scope.defaultText;
            }
          }
          if (translateAttr === 'translate') {
            // default translate into innerHTML
            if (successful || (!successful && !$translate.isKeepContent() && typeof iAttr.translateKeepContent === 'undefined')) {
              iElement.empty().append(scope.preText + value + scope.postText);
            }
            var globallyEnabled = $translate.isPostCompilingEnabled();
            var locallyDefined = typeof tAttr.translateCompile !== 'undefined';
            var locallyEnabled = locallyDefined && tAttr.translateCompile !== 'false';
            if ((globallyEnabled && !locallyDefined) || locallyEnabled) {
              $compile(iElement.contents())(scope);
            }
          } else {
            // translate attribute
            var attributeName = iAttr.$attr[translateAttr];
            if (attributeName.substr(0, 5) === 'data-') {
              // ensure html5 data prefix is stripped
              attributeName = attributeName.substr(5);
            }
            attributeName = attributeName.substr(15);
            iElement.attr(attributeName, value);
          }
        };

        if (translateValuesExist || translateValueExist || iAttr.translateDefault) {
          scope.$watch('interpolateParams', updateTranslations, true);
        }

        // Replaced watcher on translateLanguage with event listener
        scope.$on('translateLanguageChanged', updateTranslations);

        // Ensures the text will be refreshed after the current language was changed
        // w/ $translate.use(...)
        var unbind = $rootScope.$on('$translateChangeSuccess', updateTranslations);

        // ensure translation will be looked up at least one
        if (iElement.text().length) {
          if (iAttr.translate) {
            observeElementTranslation(iAttr.translate);
          } else {
            observeElementTranslation('');
          }
        } else if (iAttr.translate) {
          // ensure attribute will be not skipped
          observeElementTranslation(iAttr.translate);
        }
        updateTranslations();
        scope.$on('$destroy', unbind);
      };
    }
  };
}

/**
 * Returns the scope's namespace.
 * @private
 * @param scope
 * @returns {string}
 */
function getTranslateNamespace(scope) {
  'use strict';
  if (scope.translateNamespace) {
    return scope.translateNamespace;
  }
  if (scope.$parent) {
    return getTranslateNamespace(scope.$parent);
  }
}

translateDirective.displayName = 'translateDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translate-attr
 * @restrict A
 *
 * @description
 * Translates attributes like translate-attr-ATTR, but with an object like ng-class.
 * Internally it uses `translate` service to translate translation id. It possible to
 * pass an optional `translate-values` object literal as string into translation id.
 *
 * @param {string=} translate-attr Object literal mapping attributes to translation ids.
 * @param {string=} translate-values Values to pass into the translation ids. Can be passed as object literal string.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div ng-controller="TranslateCtrl">

        <input translate-attr="{ placeholder: translationId, title: 'WITH_VALUES' }" translate-values="{value: 5}" />

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en',{
          'TRANSLATION_ID': 'Hello there!',
          'WITH_VALUES': 'The following value is dynamic: {{value}}',
        }).preferredLanguage('en');

      });

      angular.module('ngView').controller('TranslateCtrl', function ($scope) {
        $scope.translationId = 'TRANSLATION_ID';

        $scope.values = {
          value: 78
        };
      });
    </file>
    <file name="scenario.js">
      it('should translate', function () {
        inject(function ($rootScope, $compile) {
          $rootScope.translationId = 'TRANSLATION_ID';

          element = $compile('<input translate-attr="{ placeholder: translationId, title: 'WITH_VALUES' }" translate-values="{ value: 5 }" />')($rootScope);
          $rootScope.$digest();
          expect(element.attr('placeholder)).toBe('Hello there!');
          expect(element.attr('title)).toBe('The following value is dynamic: 5');
        });
      });
    </file>
   </example>
 */
.directive('translateAttr', translateAttrDirective);
function translateAttrDirective($translate, $rootScope) {

  'use strict';

  return {
    restrict: 'A',
    priority: $translate.directivePriority(),
    link: function linkFn(scope, element, attr) {

      var translateAttr,
          translateValues,
          previousAttributes = {};

      // Main update translations function
      var updateTranslations = function () {
        angular.forEach(translateAttr, function (translationId, attributeName) {
          if (!translationId) {
            return;
          }
          previousAttributes[attributeName] = true;

          // if translation id starts with '.' and translateNamespace given, prepend namespace
          if (scope.translateNamespace && translationId.charAt(0) === '.') {
            translationId = scope.translateNamespace + translationId;
          }
          $translate(translationId, translateValues, attr.translateInterpolation, undefined, scope.translateLanguage)
            .then(function (translation) {
              element.attr(attributeName, translation);
            }, function (translationId) {
              element.attr(attributeName, translationId);
            });
        });

        // Removing unused attributes that were previously used
        angular.forEach(previousAttributes, function (flag, attributeName) {
          if (!translateAttr[attributeName]) {
            element.removeAttr(attributeName);
            delete previousAttributes[attributeName];
          }
        });
      };

      // Watch for attribute changes
      watchAttribute(
        scope,
        attr.translateAttr,
        function (newValue) { translateAttr = newValue; },
        updateTranslations
      );
      // Watch for value changes
      watchAttribute(
        scope,
        attr.translateValues,
        function (newValue) { translateValues = newValue; },
        updateTranslations
      );

      if (attr.translateValues) {
        scope.$watch(attr.translateValues, updateTranslations, true);
      }

      // Replaced watcher on translateLanguage with event listener
      scope.$on('translateLanguageChanged', updateTranslations);

      // Ensures the text will be refreshed after the current language was changed
      // w/ $translate.use(...)
      var unbind = $rootScope.$on('$translateChangeSuccess', updateTranslations);

      updateTranslations();
      scope.$on('$destroy', unbind);
    }
  };
}

function watchAttribute(scope, attribute, valueCallback, changeCallback) {
  'use strict';
  if (!attribute) {
    return;
  }
  if (attribute.substr(0, 2) === '::') {
    attribute = attribute.substr(2);
  } else {
    scope.$watch(attribute, function(newValue) {
      valueCallback(newValue);
      changeCallback();
    }, true);
  }
  valueCallback(scope.$eval(attribute));
}

translateAttrDirective.displayName = 'translateAttrDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateCloak
 * @requires $translate
 * @restrict A
 *
 * $description
 * Adds a `translate-cloak` class name to the given element where this directive
 * is applied initially and removes it, once a loader has finished loading.
 *
 * This directive can be used to prevent initial flickering when loading translation
 * data asynchronously.
 *
 * The class name is defined in
 * {@link pascalprecht.translate.$translateProvider#cloakClassName $translate.cloakClassName()}.
 *
 * @param {string=} translate-cloak If a translationId is provided, it will be used for showing
 *                                  or hiding the cloak. Basically it relies on the translation
 *                                  resolve.
 */
.directive('translateCloak', translateCloakDirective);

function translateCloakDirective($translate, $rootScope) {

  'use strict';

  return {
    compile : function (tElement) {
      var applyCloak = function (element) {
          element.addClass($translate.cloakClassName());
        },
        removeCloak = function (element) {
          element.removeClass($translate.cloakClassName());
        };
      applyCloak(tElement);

      return function linkFn(scope, iElement, iAttr) {
        //Create bound functions that incorporate the active DOM element.
        var iRemoveCloak = removeCloak.bind(this, iElement), iApplyCloak = applyCloak.bind(this, iElement);
        if (iAttr.translateCloak && iAttr.translateCloak.length) {
          // Register a watcher for the defined translation allowing a fine tuned cloak
          iAttr.$observe('translateCloak', function (translationId) {
            $translate(translationId).then(iRemoveCloak, iApplyCloak);
          });
          $rootScope.$on('$translateChangeSuccess', function () {
            $translate(iAttr.translateCloak).then(iRemoveCloak, iApplyCloak);
          });
        } else {
          $translate.onReady(iRemoveCloak);
        }
      };
    }
  };
}

translateCloakDirective.displayName = 'translateCloakDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateNamespace
 * @restrict A
 *
 * @description
 * Translates given translation id either through attribute or DOM content.
 * Internally it uses `translate` filter to translate translation id. It possible to
 * pass an optional `translate-values` object literal as string into translation id.
 *
 * @param {string=} translate namespace name which could be either string or interpolated string.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div translate-namespace="CONTENT">

        <div>
            <h1 translate>.HEADERS.TITLE</h1>
            <h1 translate>.HEADERS.WELCOME</h1>
        </div>

        <div translate-namespace=".HEADERS">
            <h1 translate>.TITLE</h1>
            <h1 translate>.WELCOME</h1>
        </div>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en',{
          'TRANSLATION_ID': 'Hello there!',
          'CONTENT': {
            'HEADERS': {
                TITLE: 'Title'
            }
          },
          'CONTENT.HEADERS.WELCOME': 'Welcome'
        }).preferredLanguage('en');

      });

    </file>
   </example>
 */
.directive('translateNamespace', translateNamespaceDirective);

function translateNamespaceDirective() {

  'use strict';

  return {
    restrict: 'A',
    scope: true,
    compile: function () {
      return {
        pre: function (scope, iElement, iAttrs) {
          scope.translateNamespace = getTranslateNamespace(scope);

          if (scope.translateNamespace && iAttrs.translateNamespace.charAt(0) === '.') {
            scope.translateNamespace += iAttrs.translateNamespace;
          } else {
            scope.translateNamespace = iAttrs.translateNamespace;
          }
        }
      };
    }
  };
}

/**
 * Returns the scope's namespace.
 * @private
 * @param scope
 * @returns {string}
 */
function getTranslateNamespace(scope) {
  'use strict';
  if (scope.translateNamespace) {
    return scope.translateNamespace;
  }
  if (scope.$parent) {
    return getTranslateNamespace(scope.$parent);
  }
}

translateNamespaceDirective.displayName = 'translateNamespaceDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc directive
 * @name pascalprecht.translate.directive:translateLanguage
 * @restrict A
 *
 * @description
 * Forces the language to the directives in the underlying scope.
 *
 * @param {string=} translate language that will be negotiated.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div>

        <div>
            <h1 translate>HELLO</h1>
        </div>

        <div translate-language="de">
            <h1 translate>HELLO</h1>
        </div>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider
          .translations('en',{
            'HELLO': 'Hello world!'
          })
          .translations('de',{
            'HELLO': 'Hallo Welt!'
          })
          .preferredLanguage('en');

      });

    </file>
   </example>
 */
.directive('translateLanguage', translateLanguageDirective);

function translateLanguageDirective() {

  'use strict';

  return {
    restrict: 'A',
    scope: true,
    compile: function () {
      return function linkFn(scope, iElement, iAttrs) {

        iAttrs.$observe('translateLanguage', function (newTranslateLanguage) {
          scope.translateLanguage = newTranslateLanguage;
        });

        scope.$watch('translateLanguage', function(){
          scope.$broadcast('translateLanguageChanged');
        });
      };
    }
  };
}

translateLanguageDirective.displayName = 'translateLanguageDirective';

angular.module('pascalprecht.translate')
/**
 * @ngdoc filter
 * @name pascalprecht.translate.filter:translate
 * @requires $parse
 * @requires pascalprecht.translate.$translate
 * @function
 *
 * @description
 * Uses `$translate` service to translate contents. Accepts interpolate parameters
 * to pass dynamized values though translation.
 *
 * @param {string} translationId A translation id to be translated.
 * @param {*=} interpolateParams Optional object literal (as hash or string) to pass values into translation.
 *
 * @returns {string} Translated text.
 *
 * @example
   <example module="ngView">
    <file name="index.html">
      <div ng-controller="TranslateCtrl">

        <pre>{{ 'TRANSLATION_ID' | translate }}</pre>
        <pre>{{ translationId | translate }}</pre>
        <pre>{{ 'WITH_VALUES' | translate:'{value: 5}' }}</pre>
        <pre>{{ 'WITH_VALUES' | translate:values }}</pre>

      </div>
    </file>
    <file name="script.js">
      angular.module('ngView', ['pascalprecht.translate'])

      .config(function ($translateProvider) {

        $translateProvider.translations('en', {
          'TRANSLATION_ID': 'Hello there!',
          'WITH_VALUES': 'The following value is dynamic: {{value}}'
        });
        $translateProvider.preferredLanguage('en');

      });

      angular.module('ngView').controller('TranslateCtrl', function ($scope) {
        $scope.translationId = 'TRANSLATION_ID';

        $scope.values = {
          value: 78
        };
      });
    </file>
   </example>
 */
.filter('translate', translateFilterFactory);

function translateFilterFactory($parse, $translate) {

  'use strict';

  var translateFilter = function (translationId, interpolateParams, interpolation, forceLanguage) {
    if (!angular.isObject(interpolateParams)) {
      var ctx = this || {
        '__SCOPE_IS_NOT_AVAILABLE': 'More info at https://github.com/angular/angular.js/commit/8863b9d04c722b278fa93c5d66ad1e578ad6eb1f'
        };
      interpolateParams = $parse(interpolateParams)(ctx);
    }

    return $translate.instant(translationId, interpolateParams, interpolation, forceLanguage);
  };

  if ($translate.statefulFilter()) {
    translateFilter.$stateful = true;
  }

  return translateFilter;
}

translateFilterFactory.displayName = 'translateFilterFactory';

angular.module('pascalprecht.translate')

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translationCache
 * @requires $cacheFactory
 *
 * @description
 * The first time a translation table is used, it is loaded in the translation cache for quick retrieval. You
 * can load translation tables directly into the cache by consuming the
 * `$translationCache` service directly.
 *
 * @return {object} $cacheFactory object.
 */
  .factory('$translationCache', $translationCache);

function $translationCache($cacheFactory) {

  'use strict';

  return $cacheFactory('translations');
}

$translationCache.displayName = '$translationCache';
return 'pascalprecht.translate';

}));

angular.module('validation', ['validation.provider', 'validation.directive']);
angular.module('validation.provider', []);
angular.module('validation.directive', ['validation.provider']);

(function() {
  angular
    .module('validation.provider')
    .provider('$validation', Provider);

  function Provider() {
    var $injector;
    var $scope;
    var $http;
    var $q;
    var $timeout;
    var _this = this;

    /**
     * Setup the provider
     * @param injector
     */
    var setup = function(injector) {
      $injector = injector;
      $scope = $injector.get('$rootScope');
      $http = $injector.get('$http');
      $q = $injector.get('$q');
      $timeout = $injector.get('$timeout');
    };

    /**
     * Define validation type RegExp
     * @type {{}}
     */
    var expression = {};

    /**
     * default valid method
     * @type {{}}
     */
    var validMethod = null;

    /**
     * default error, success message
     * @type {{}}
     */
    var defaultMsg = {};

    /**
     * Allow user to set a custom Expression, do remember set the default message using setDefaultMsg
     * @param obj
     * @returns {*}
     */
    this.setExpression = function(obj) {
      angular.extend(expression, obj);
      return _this;
    };

    /**
     * Get the Expression
     * @param exprs
     * @returns {*}
     */
    this.getExpression = function(exprs) {
      return expression[exprs];
    };

    /**
     * Allow user to set default message
     * @param obj
     * @returns {*}
     */
    this.setDefaultMsg = function(obj) {
      angular.extend(defaultMsg, obj);
      return _this;
    };

    /**
     * Get the Default Message
     * @param msg
     * @returns {*}
     */
    this.getDefaultMsg = function(msg) {
      return defaultMsg[msg];
    };

    /**
     * allow user to set the global valid method
     * @param v
     * @returns {*}
     */
    this.setValidMethod = function(v) {
      validMethod = v;
    };

    /**
     * Get the valid method     
     * @returns {*}
     */
    this.getValidMethod = function() {
      return validMethod;
    };

    /**
     * Override the errorHTML function
     * @param func
     * @returns {*}
     */
    this.setErrorHTML = function(func) {
      if (func.constructor !== Function) {
        return;
      }
      _this.getErrorHTML = func;
      return _this;
    };

    /**
     * Invalid message HTML, here's the default
     * @param message
     * @returns {string}
     */
    this.getErrorHTML = function(message) {
      return '<p class="validation-invalid">' + message + '</p>';
    };

    /**
     * Override the successHTML function
     * @param func
     * @returns {*}
     */
    this.setSuccessHTML = function(func) {
      if (func.constructor !== Function) {
        return;
      }
      _this.getSuccessHTML = func;
      return _this;
    };

    /**
     * Valid message HTML, here's the default
     * @param message
     * @returns {string}
     */
    this.getSuccessHTML = function(message) {
      return '<p class="validation-valid">' + message + '</p>';
    };

    /**
     * Whether show the validation success message
     * You can easily change this to false in your config
     * example: $validationProvider.showSuccessMessage = false;
     * @type {boolean}
     */
    this.showSuccessMessage = true;

    /**
     * Whether show the validation error message
     * You can easily change this to false in your config
     * example: $validationProvider.showErrorMessage = false;
     * @type {boolean}
     */
    this.showErrorMessage = true;

    /**
     * Check form valid, return true
     * checkValid(Form): Check the specific form(Form) valid from angular `$valid`
     * @param form
     * @returns {boolean}
     */
    this.checkValid = function(form) {
      return !!(form && form.$valid);
    };

    /**
     * Validate the form when click submit, when `validMethod = submit`
     * @param form
     * @returns {promise|*}
     */
    this.validate = function(form) {
      var deferred = $q.defer();
      var idx = 0;

      if (form === undefined) {
        console.error('This is not a regular Form name scope');
        deferred.reject('This is not a regular Form name scope');
        return deferred.promise;
      }

      if (form.validationId) { // single
        $scope.$broadcast(form.$name + 'submit-' + form.validationId, idx++);
      } else if (form.constructor === Array) { // multiple
        for (var k in form) {
          $scope.$broadcast(form[k].$name + 'submit-' + form[k].validationId, idx++);
        }
      } else {
        for (var i in form) { // whole scope
          if (i[0] !== '$' && form[i].hasOwnProperty('$dirty')) {
            $scope.$broadcast(i + 'submit-' + form[i].validationId, idx++);
          }
        }
      }

      deferred.promise.success = function(fn) {
        deferred.promise.then(function(value) {
          fn(value);
        });
        return deferred.promise;
      };

      deferred.promise.error = function(fn) {
        deferred.promise.then(null, function(value) {
          fn(value);
        });
        return deferred.promise;
      };

      $timeout(function() {
        if (_this.checkValid(form)) {
          deferred.resolve('success');
        } else {
          deferred.reject('error');
        }
      });

      return deferred.promise;
    };

    /**
     * Do this function if validation valid
     * @param element
     */
    this.validCallback = null;

    /**
     * Do this function if validation invalid
     * @param element
     */
    this.invalidCallback = null;

    /**
     * Do this function when reset is performed
     * @param element
     */
    this.resetCallback = null;

    /**
     * reset the specific form
     * @param form
     */
    this.reset = function(form) {
      if (form === undefined) {
        console.error('This is not a regular Form name scope');
        return;
      }

      if (form.validationId) {
        $scope.$broadcast(form.$name + 'reset-' + form.validationId);
      } else if (form.constructor === Array) {
        for (var k in form) {
          $scope.$broadcast(form[k].$name + 'reset-' + form[k].validationId);
        }
      } else {
        for (var i in form) {
          if (i[0] !== '$' && form[i].hasOwnProperty('$dirty')) {
            $scope.$broadcast(i + 'reset-' + form[i].validationId);
          }
        }
      }
    };

    /**
     * Add Message Element in config phase
     * When you need custom your messageElement 
     * NODE: this funtion & and `message-id` attribute, have similar purpose.
     * This function will help you add your `messageElement` automatically instead of pre-defined.
     * @param element
     */
    this.addMsgElement = function(element) {
      return element.after('<span></span>');
    };

    /**
     * Add Message Element in config phase
     * When you need custom your messageElement 
     * NODE: this funtion & and `message-id` attribute, have similar purpose.
     * This function will help you add your `messageElement` automatically instead of pre-defined.
     * @param element
     */
    this.getMsgElement = function(element) {
      return element.next();
    };

    /**
     * $get
     * @returns {{setErrorHTML: *, getErrorHTML: Function, setSuccessHTML: *, getSuccessHTML: Function, setExpression: *, getExpression: Function, setDefaultMsg: *, getDefaultMsg: Function, checkValid: Function, validate: Function, reset: Function}}
     */
    this.$get = ['$injector', function($injector) {
      setup($injector);
      return {
        setValidMethod: this.setValidMethod,
        getValidMethod: this.getValidMethod,
        setErrorHTML: this.setErrorHTML,
        getErrorHTML: this.getErrorHTML,
        setSuccessHTML: this.setSuccessHTML,
        getSuccessHTML: this.getSuccessHTML,
        setExpression: this.setExpression,
        getExpression: this.getExpression,
        setDefaultMsg: this.setDefaultMsg,
        getDefaultMsg: this.getDefaultMsg,
        showSuccessMessage: this.showSuccessMessage,
        showErrorMessage: this.showErrorMessage,
        checkValid: this.checkValid,
        validate: this.validate,
        validCallback: this.validCallback,
        invalidCallback: this.invalidCallback,
        resetCallback: this.resetCallback,
        reset: this.reset,
        addMsgElement: this.addMsgElement,
        getMsgElement: this.getMsgElement
      };
    }];
  }
}).call(this);

(function() {
  angular
    .module('validation.directive')
    .directive('validationReset', Reset);

  function Reset($injector) {
    var $validationProvider = $injector.get('$validation');
    var $timeout = $injector.get('$timeout');
    var $parse = $injector.get('$parse');
    return {
      link: function postLink(scope, element, attrs) {
        var form = $parse(attrs.validationReset)(scope);
        $timeout(function() {
          element.on('click', function(e) {
            e.preventDefault();
            $validationProvider.reset(form);
          });
        });
      }
    };
  }
  Reset.$inject = ['$injector'];
}).call(this);

(function() {
  angular
    .module('validation.directive')
    .directive('validationSubmit', Submit);

  function Submit($injector) {
    var $validationProvider = $injector.get('$validation');
    var $timeout = $injector.get('$timeout');
    var $parse = $injector.get('$parse');
    return {
      priority: 1, // execute before ng-click (0)
      require: '?ngClick',
      link: function postLink(scope, element, attrs) {
        var form = $parse(attrs.validationSubmit)(scope);
        $timeout(function() {
          // Disable ng-click event propagation
          element.off('click');
          element.on('click', function(e) {
            e.preventDefault();
            $validationProvider.validate(form)
              .success(function() {
                $parse(attrs.ngClick)(scope);
              });
          });
        });
      }
    };
  }
  Submit.$inject = ['$injector'];
}).call(this);

(function() {
  angular
    .module('validation.directive')
    .directive('validator', Validator);

  function Validator($injector) {
    var $validationProvider = $injector.get('$validation');
    var $q = $injector.get('$q');
    var $timeout = $injector.get('$timeout');
    var $compile = $injector.get('$compile');
    var $parse = $injector.get('$parse');
    var groups = {};

    /**
     * Do this function if validation valid
     * @param element
     * @param validMessage
     * @param validation
     * @param callback
     * @param ctrl
     * @returns {}
     */
    var validFunc = function(element, validMessage, validation, scope, ctrl, attrs, param) {
      var messageToShow = validMessage || $validationProvider.getDefaultMsg(validation).success;
      var validCallback = $parse(attrs.validCallback);
      var messageId = attrs.messageId;
      var validationGroup = attrs.validationGroup;
      var messageElem;

      if (messageId || validationGroup) messageElem = angular.element(document.querySelector('#' + (messageId || validationGroup)));
      else messageElem = $validationProvider.getMsgElement(element);

      if (element.attr('no-validation-message')) {
        messageElem.css('display', 'none');
      } else if ($validationProvider.showSuccessMessage && messageToShow) {
        messageToShow = angular.isFunction(messageToShow) ? messageToShow(element, attrs, param) : messageToShow;

        messageElem.html('').append($compile($validationProvider.getSuccessHTML(messageToShow, element, attrs))(scope));
        messageElem.css('display', '');
      } else {
        messageElem.css('display', 'none');
      }

      ctrl.$setValidity(ctrl.$name, true);
      validCallback(scope, {
        message: messageToShow
      });
      if ($validationProvider.validCallback) $validationProvider.validCallback(element);

      return true;
    };


    /**
     * Do this function if validation invalid
     * @param element
     * @param validMessage
     * @param validation
     * @param callback
     * @param ctrl
     * @returns {}
     */
    var invalidFunc = function(element, validMessage, validation, scope, ctrl, attrs, param) {
      var messageToShow = validMessage || $validationProvider.getDefaultMsg(validation).error;
      var invalidCallback = $parse(attrs.invalidCallback);
      var messageId = attrs.messageId;
      var validationGroup = attrs.validationGroup;
      var messageElem;

      if (messageId || validationGroup) messageElem = angular.element(document.querySelector('#' + (messageId || validationGroup)));
      else messageElem = $validationProvider.getMsgElement(element);

      if (element.attr('no-validation-message')) {
        messageElem.css('display', 'none');
      } else if ($validationProvider.showErrorMessage && messageToShow) {
        messageToShow = angular.isFunction(messageToShow) ? messageToShow(element, attrs, param) : messageToShow;

        messageElem.html('').append($compile($validationProvider.getErrorHTML(messageToShow, element, attrs))(scope));
        messageElem.css('display', '');
      } else {
        messageElem.css('display', 'none');
      }

      ctrl.$setValidity(ctrl.$name, false);
      invalidCallback(scope, {
        message: messageToShow
      });
      if ($validationProvider.invalidCallback) $validationProvider.invalidCallback(element);

      return false;
    };

    /**
     * Verify whether there is one of the elements inside the group valid.
     * If so, it returns true, otherwise, it returns false
     *
     * @param validationGroup
     * @return {boolean}
     */
    var checkValidationGroup = function(validationGroup) {
      var group = groups[validationGroup];

      return Object.keys(group).some(function(key) {
        return group[key];
      });
    };

    /**
     * Set validity to all elements inside the given group
     *
     * @param scope
     * @param groupName
     * @param validity
     */
    function setValidationGroup(scope, validationGroup, validity) {
      var validationGroupElems = document.querySelectorAll('*[validation-group=' + validationGroup + ']');

      // Loop through all elements inside the group
      for (var i = 0, len = validationGroupElems.length; i < len; i++) {
        var elem = validationGroupElems[i];
        var formName = elem.form.name;
        var elemName = elem.name;
        scope[formName][elemName].$setValidity(elemName, validity);
      }
    }

    /**
     * collect elements for focus
     * @type {Object}
     ***private variable
     */
    var focusElements = {};

    /**
     * Get Validation Result Object
     * @param data
     * @returns {
     *    result: Boolean, // is success or error
     *    message: String  // tips
     * }
     */
    function getResultObj(data) {
      var res = {};
      if (data && data.length > 0) {
        res = data[0];
        if (!angular.isObject(res)) {
          res = {
            result: res,
            message: ''
          };
        }
      } else {
        res = {
          result: false,
          message: ''
        };
      }
      return res;
    }

    /**
     * Check Validation with Function or RegExp
     * @param scope
     * @param element
     * @param attrs
     * @param ctrl
     * @param validation
     * @param value
     * @returns {}
     */
    var checkValidation = function(scope, element, attrs, ctrl, validation, value) {
      var validators = validation.slice(0);
      var validatorExpr = validators[0].trim();
      var paramIndex = validatorExpr.indexOf('=');
      var validator = paramIndex === -1 ? validatorExpr : validatorExpr.substr(0, paramIndex);
      var validatorParam = paramIndex === -1 ? null : validatorExpr.substr(paramIndex + 1);
      var leftValidation = validators.slice(1);
      var successMessage = validator + 'SuccessMessage';
      var errorMessage = validator + 'ErrorMessage';
      var expression = $validationProvider.getExpression(validator);
      var validationGroup = attrs.validationGroup;
      var valid = {
        success: function(message) {
          validFunc(element, message || attrs[successMessage], validator, scope, ctrl, attrs, validatorParam);
          if (leftValidation.length) {
            return checkValidation(scope, element, attrs, ctrl, leftValidation, value);
          } else {
            return true;
          }
        },
        error: function(message) {
          return invalidFunc(element, message || attrs[errorMessage], validator, scope, ctrl, attrs, validatorParam);
        }
      };

      if (expression === undefined) {
        console.error('You are using undefined validator "%s"', validator);
        if (leftValidation.length) return checkValidation(scope, element, attrs, ctrl, leftValidation, value);
        else return;
      }
      // Check with Function
      if (expression.constructor === Function) {
        return $q.all([$validationProvider.getExpression(validator)(value, scope, element, attrs, validatorParam)])
          .then(function(data) {
            var resultObj = getResultObj(data);
            var message = resultObj.message;
            if (resultObj.result) {
              if (validationGroup) {
                groups[validationGroup][ctrl.$name] = true;
                setValidationGroup(scope, validationGroup, true);
              }
              return valid.success(message);
            } else if (validationGroup) {
              groups[validationGroup][ctrl.$name] = false;

              // Whenever the element is invalid, we'll check whether one of the elements inside the its group valid or not.
              // If there is a valid element, its invalid message won't be shown, Otherwise, shows its invalid message.
              if (checkValidationGroup(validationGroup)) {
                setValidationGroup(scope, validationGroup, true);
              } else {
                setValidationGroup(scope, validationGroup, false);
                return valid.error(message);
              }
            } else return valid.error(message);
          }, function() {
            return valid.error();
          });
      }

      // Check with RegExp
      else if (expression.constructor === RegExp) {
        // Only apply the test if the value is neither undefined or null
        if (value !== undefined && value !== null) {
          if ($validationProvider.getExpression(validator).test(value)) {
            if (validationGroup) {
              groups[validationGroup][ctrl.$name] = true;
              setValidationGroup(scope, validationGroup, true);
            }
            return valid.success();
          } else if (validationGroup) {
            groups[validationGroup][ctrl.$name] = false;

            // Whenever the element is invalid, we'll check whether one of the elements inside the its group valid or not.
            // If there is a valid element, its invalid message won't be shown, Otherwise, shows its invalid message.
            if (checkValidationGroup(validationGroup)) {
              setValidationGroup(scope, validationGroup, true);
            } else {
              setValidationGroup(scope, validationGroup, false);
              return valid.error();
            }
          } else return valid.error();
        }
      } else return valid.error();
    };

    /**
     * generate unique guid
     */
    var s4 = function() {
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    };
    var guid = function() {
      return (s4() + s4() + s4() + s4());
    };


    return {
      restrict: 'A',
      require: 'ngModel',
      link: function(scope, element, attrs, ctrl) {
        /**
         * All attributes
         */
        var useViewValue = attrs.useViewValue !== 'false';
        var validator = attrs.validator;
        var messageId = attrs.messageId;
        var validationGroup = attrs.validationGroup;
        var validMethod = attrs.validMethod;
        var ngModel = attrs.ngModel;

        /**
         * watch
         * @type {watch}
         *
         * Use to collect scope.$watch method
         *
         * use watch() to destroy the $watch method
         */
        var watch = function() {};

        /**
         * validator
         * @type {Array}
         *
         * Convert user input String to Array
         */
        var validation = validator.split(',');

        /**
         * guid use
         */
        var uid = ctrl.validationId = guid();

        /**
         * to have a value to rollback to
         */
        var originalViewValue = null;

        /**
         * Set initial validity to undefined if no boolean value is transmitted
         */
        var initialValidity = void 0;
        if (typeof attrs.initialValidity === 'boolean') {
          initialValidity = attrs.initialValidity;
        }

        /**
         * Observe validator changes in order to allow dynamically change it
         */
        attrs.$observe('validator', function(value) {
          validation = value.split(',');
        });

        /**
         * Set up groups object in order to keep track validation of elements
         */
        if (validationGroup) {
          if (!groups[validationGroup]) groups[validationGroup] = {};
          groups[validationGroup][ctrl.$name] = false;
        }

        /**
         * Default Valid/Invalid Message
         */
        if (!(messageId || validationGroup)) $validationProvider.addMsgElement(element);

        /**
         * Set custom initial validity
         * Usage: <input initial-validity="true" ... >
         */
        ctrl.$setValidity(ctrl.$name, initialValidity);

        /**
         * Reset the validation for specific form
         */
        scope.$on(ctrl.$name + 'reset-' + uid, function() {
          /**
           * clear scope.$watch here
           * when reset status
           * clear the $watch method to prevent
           * $watch again while reset the form
           */
          watch();

          $timeout(function() {
            ctrl.$setViewValue(originalViewValue);
            ctrl.$setPristine();
            ctrl.$setValidity(ctrl.$name, undefined);
            ctrl.$render();
            if (messageId || validationGroup) angular.element(document.querySelector('#' + (messageId || validationGroup))).html('');
            else $validationProvider.getMsgElement(element).html('');

            if ($validationProvider.resetCallback) $validationProvider.resetCallback(element);
          });
        });

        /**
         * Check validator
         */
        validMethod = (angular.isUndefined(validMethod)) ? $validationProvider.getValidMethod() : validMethod;

        /**
         * Click submit form, check the validity when submit
         */
        scope.$on(ctrl.$name + 'submit-' + uid, function(event, index) {
          var value = useViewValue ? ctrl.$viewValue : ctrl.$modelValue;
          var isValid = false;

          isValid = checkValidation(scope, element, attrs, ctrl, validation, value);

          if (validMethod === 'submit') {
            // clear previous scope.$watch
            watch();
            watch = scope.$watch(function() {
              return scope.$eval(ngModel);
            }, function(value, oldValue) {
              // don't watch when init
              if (value === oldValue) {
                return;
              }

              // scope.$watch will translate '' to undefined
              // undefined/null will pass the required submit /^.+/
              // cause some error in this validation
              if (value === undefined || value === null) {
                value = '';
              }

              isValid = checkValidation(scope, element, attrs, ctrl, validation, value);
            });
          }

          var setFocus = function(isValid) {
            if (isValid) {
              delete focusElements[index];
            } else {
              focusElements[index] = element[0];

              $timeout(function() {
                focusElements[Math.min.apply(null, Object.keys(focusElements))].focus();
              }, 0);
            }
          };

          if (isValid instanceof Object) isValid.then(setFocus);
          else setFocus(isValid);
        });

        /**
         * Validate blur method
         */
        if (validMethod === 'blur') {
          element.bind('blur', function() {
            var value = scope.$eval(ngModel);

            if (scope.$root.$$phase !== '$apply') {
              scope.$apply(function() {
                checkValidation(scope, element, attrs, ctrl, validation, value);
              });
            } else {
              checkValidation(scope, element, attrs, ctrl, validation, value);
            }
          });

          return;
        }

        /**
         * Validate submit & submit-only method
         */
        if (validMethod === 'submit' || validMethod === 'submit-only') {
          return;
        }

        /**
         * Validate watch method
         * This is the default method
         */
        scope.$watch(function() {
          return scope.$eval(ngModel);
        }, function(value) {
          /**
           * dirty, pristine, viewValue control here
           */
          if (ctrl.$pristine && ctrl.$viewValue) {
            // has value when initial
            originalViewValue = ctrl.$viewValue || '';
            ctrl.$setViewValue(ctrl.$viewValue);
          } else if (ctrl.$pristine) {
            // Don't validate form when the input is clean(pristine)
            if (messageId || validationGroup) angular.element(document.querySelector('#' + (messageId || validationGroup))).html('');
            else $validationProvider.getMsgElement(element).html('');
            return;
          }
          checkValidation(scope, element, attrs, ctrl, validation, value);
        });

        $timeout(function() {
          /**
           * Don't showup the validation Message
           */
          attrs.$observe('noValidationMessage', function(value) {
            var el;
            if (messageId || validationGroup) el = angular.element(document.querySelector('#' + (messageId || validationGroup)));
            else el = $validationProvider.getMsgElement(element);
            if (value === 'true' || value === true) el.css('display', 'none');
            else if (value === 'false' || value === false) el.css('display', 'block');
          });
        });
      }
    };
  }
  Validator.$inject = ['$injector'];
}).call(this);

(function() {
  angular
    .module('validation.rule', ['validation'])
    .config(['$validationProvider', function($validationProvider) {
      var expression = {
        required: function(value) {
          return !!value;
        },
        url: /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/,
        email: /^([\w-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/,
        number: /^\d+$/,
        minlength: function(value, scope, element, attrs, param) {
          return value && value.length >= param;
        },
        maxlength: function(value, scope, element, attrs, param) {
          return !value || value.length <= param;
        }
      };

      var defaultMsg = {
        required: {
          error: 'This should be Required!!',
          success: 'It\'s Required'
        },
        url: {
          error: 'This should be Url',
          success: 'It\'s Url'
        },
        email: {
          error: 'This should be Email',
          success: 'It\'s Email'
        },
        number: {
          error: 'This should be Number',
          success: 'It\'s Number'
        },
        minlength: {
          error: 'This should be longer',
          success: 'Long enough!'
        },
        maxlength: {
          error: 'This should be shorter',
          success: 'Short enough!'
        }
      };
      $validationProvider.setExpression(expression).setDefaultMsg(defaultMsg);
    }]);
}).call(this);

/*!
 * angular-translate - v2.15.2 - 2017-06-22
 * 
 * Copyright (c) 2017 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

$translateStaticFilesLoader.$inject = ['$q', '$http'];
angular.module('pascalprecht.translate')
/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateStaticFilesLoader
 * @requires $q
 * @requires $http
 *
 * @description
 * Creates a loading function for a typical static file url pattern:
 * "lang-en_US.json", "lang-de_DE.json", etc. Using this builder,
 * the response of these urls must be an object of key-value pairs.
 *
 * @param {object} options Options object, which gets prefix, suffix, key, and fileMap
 */
.factory('$translateStaticFilesLoader', $translateStaticFilesLoader);

function $translateStaticFilesLoader($q, $http) {

  'use strict';

  return function (options) {

    if (!options || (!angular.isArray(options.files) && (!angular.isString(options.prefix) || !angular.isString(options.suffix)))) {
      throw new Error('Couldn\'t load static files, no files and prefix or suffix specified!');
    }

    if (!options.files) {
      options.files = [{
        prefix: options.prefix,
        suffix: options.suffix
      }];
    }

    var load = function (file) {
      if (!file || (!angular.isString(file.prefix) || !angular.isString(file.suffix))) {
        throw new Error('Couldn\'t load static file, no prefix or suffix specified!');
      }

      var fileUrl = [
        file.prefix,
        options.key,
        file.suffix
      ].join('');

      if (angular.isObject(options.fileMap) && options.fileMap[fileUrl]) {
        fileUrl = options.fileMap[fileUrl];
      }

      return $http(angular.extend({
        url: fileUrl,
        method: 'GET'
      }, options.$http))
        .then(function(result) {
          return result.data;
        }, function () {
          return $q.reject(options.key);
        });
    };

    var promises = [],
        length = options.files.length;

    for (var i = 0; i < length; i++) {
      promises.push(load({
        prefix: options.files[i].prefix,
        key: options.key,
        suffix: options.files[i].suffix
      }));
    }

    return $q.all(promises)
      .then(function (data) {
        var length = data.length,
            mergedData = {};

        for (var i = 0; i < length; i++) {
          for (var key in data[i]) {
            mergedData[key] = data[i][key];
          }
        }

        return mergedData;
      });
  };
}

$translateStaticFilesLoader.displayName = '$translateStaticFilesLoader';
return 'pascalprecht.translate';

}));

/*!
 * angular-translate - v2.15.2 - 2017-06-22
 * 
 * Copyright (c) 2017 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

$translateCookieStorageFactory.$inject = ['$injector'];
angular.module('pascalprecht.translate')

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateCookieStorage
 * @requires $cookieStore
 *
 * @description
 * Abstraction layer for cookieStore. This service is used when telling angular-translate
 * to use cookieStore as storage.
 *
 */
  .factory('$translateCookieStorage', $translateCookieStorageFactory);

function $translateCookieStorageFactory($injector) {

  'use strict';

  // Since AngularJS 1.4, $cookieStore is deprecated
  var delegate;
  if (angular.version.major === 1 && angular.version.minor >= 4) {
    var $cookies = $injector.get('$cookies');
    delegate = {
      get : function (key) {
        return $cookies.get(key);
      },
      put : function (key, value) {
        $cookies.put(key, value);
      }
    };
  } else {
    var $cookieStore = $injector.get('$cookieStore');
    delegate = {
      get : function (key) {
        return $cookieStore.get(key);
      },
      put : function (key, value) {
        $cookieStore.put(key, value);
      }
    };
  }

  var $translateCookieStorage = {

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateCookieStorage#get
     * @methodOf pascalprecht.translate.$translateCookieStorage
     *
     * @description
     * Returns an item from cookieStorage by given name.
     *
     * @param {string} name Item name
     * @return {string} Value of item name
     */
    get : function (name) {
      return delegate.get(name);
    },

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateCookieStorage#set
     * @methodOf pascalprecht.translate.$translateCookieStorage
     *
     * @description
     * Sets an item in cookieStorage by given name.
     *
     * @deprecated use #put
     *
     * @param {string} name Item name
     * @param {string} value Item value
     */
    set : function (name, value) {
      delegate.put(name, value);
    },

    /**
     * @ngdoc function
     * @name pascalprecht.translate.$translateCookieStorage#put
     * @methodOf pascalprecht.translate.$translateCookieStorage
     *
     * @description
     * Sets an item in cookieStorage by given name.
     *
     * @param {string} name Item name
     * @param {string} value Item value
     */
    put : function (name, value) {
      delegate.put(name, value);
    }
  };

  return $translateCookieStorage;
}

$translateCookieStorageFactory.displayName = '$translateCookieStorage';
return 'pascalprecht.translate';

}));

/*!
 * angular-translate - v2.15.2 - 2017-06-22
 * 
 * Copyright (c) 2017 The angular-translate team, Pascal Precht; Licensed MIT
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD. Register as an anonymous module unless amdModuleId is set
    define([], function () {
      return (factory());
    });
  } else if (typeof module === 'object' && module.exports) {
    // Node. Does not work with strict CommonJS, but
    // only CommonJS-like environments that support module.exports,
    // like Node.
    module.exports = factory();
  } else {
    factory();
  }
}(this, function () {

$translateLocalStorageFactory.$inject = ['$window', '$translateCookieStorage'];
angular.module('pascalprecht.translate')

/**
 * @ngdoc object
 * @name pascalprecht.translate.$translateLocalStorage
 * @requires $window
 * @requires $translateCookieStorage
 *
 * @description
 * Abstraction layer for localStorage. This service is used when telling angular-translate
 * to use localStorage as storage.
 *
 */
.factory('$translateLocalStorage', $translateLocalStorageFactory);

function $translateLocalStorageFactory($window, $translateCookieStorage) {

  'use strict';

  // Setup adapter
  var localStorageAdapter = (function(){
    var langKey;
    return {
      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateLocalStorage#get
       * @methodOf pascalprecht.translate.$translateLocalStorage
       *
       * @description
       * Returns an item from localStorage by given name.
       *
       * @param {string} name Item name
       * @return {string} Value of item name
       */
      get: function (name) {
        if(!langKey) {
          langKey = $window.localStorage.getItem(name);
        }

        return langKey;
      },
      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateLocalStorage#set
       * @methodOf pascalprecht.translate.$translateLocalStorage
       *
       * @description
       * Sets an item in localStorage by given name.
       *
       * @deprecated use #put
       *
       * @param {string} name Item name
       * @param {string} value Item value
       */
      set: function (name, value) {
        langKey=value;
        $window.localStorage.setItem(name, value);
      },
      /**
       * @ngdoc function
       * @name pascalprecht.translate.$translateLocalStorage#put
       * @methodOf pascalprecht.translate.$translateLocalStorage
       *
       * @description
       * Sets an item in localStorage by given name.
       *
       * @param {string} name Item name
       * @param {string} value Item value
       */
      put: function (name, value) {
        langKey=value;
        $window.localStorage.setItem(name, value);
      }
    };
  }());

  var hasLocalStorageSupport = 'localStorage' in $window;
  if (hasLocalStorageSupport) {
    var testKey = 'pascalprecht.translate.storageTest';
    try {
      // this check have to be wrapped within a try/catch because on
      // a SecurityError: Dom Exception 18 on iOS
      if ($window.localStorage !== null) {
        $window.localStorage.setItem(testKey, 'foo');
        $window.localStorage.removeItem(testKey);
        hasLocalStorageSupport = true;
      } else {
        hasLocalStorageSupport = false;
      }
    } catch (e){
      hasLocalStorageSupport = false;
    }
  }
  var $translateLocalStorage = hasLocalStorageSupport ? localStorageAdapter : $translateCookieStorage;
  return $translateLocalStorage;
}

$translateLocalStorageFactory.displayName = '$translateLocalStorageFactory';
return 'pascalprecht.translate';

}));

/*!
 * AngularJS Material Design
 * https://github.com/angular/material
 * @license MIT
 * v1.1.5
 */
(function( window, angular, undefined ){
"use strict";

(function(){
"use strict";

angular.module('ngMaterial', ["ng","ngAnimate","ngAria","material.core","material.core.gestures","material.core.interaction","material.core.layout","material.core.meta","material.core.theming.palette","material.core.theming","material.core.animate","material.components.autocomplete","material.components.backdrop","material.components.bottomSheet","material.components.button","material.components.card","material.components.chips","material.components.checkbox","material.components.colors","material.components.content","material.components.datepicker","material.components.dialog","material.components.divider","material.components.fabActions","material.components.fabShared","material.components.fabSpeedDial","material.components.fabToolbar","material.components.icon","material.components.gridList","material.components.input","material.components.list","material.components.menu","material.components.menuBar","material.components.navBar","material.components.panel","material.components.progressCircular","material.components.progressLinear","material.components.radioButton","material.components.select","material.components.showHide","material.components.sidenav","material.components.slider","material.components.sticky","material.components.subheader","material.components.swipe","material.components.switch","material.components.tabs","material.components.toast","material.components.toolbar","material.components.tooltip","material.components.truncate","material.components.virtualRepeat","material.components.whiteframe"]);
})();
(function(){
"use strict";

/**
 * Initialization function that validates environment
 * requirements.
 */
DetectNgTouch.$inject = ["$log", "$injector"];
MdCoreConfigure.$inject = ["$provide", "$mdThemingProvider"];
rAFDecorator.$inject = ["$delegate"];
qDecorator.$inject = ["$delegate"];
angular
  .module('material.core', [
    'ngAnimate',
    'material.core.animate',
    'material.core.layout',
    'material.core.interaction',
    'material.core.gestures',
    'material.core.theming'
  ])
  .config(MdCoreConfigure)
  .run(DetectNgTouch);


/**
 * Detect if the ng-Touch module is also being used.
 * Warn if detected.
 * @ngInject
 */
function DetectNgTouch($log, $injector) {
  if ( $injector.has('$swipe') ) {
    var msg = "" +
      "You are using the ngTouch module. \n" +
      "AngularJS Material already has mobile click, tap, and swipe support... \n" +
      "ngTouch is not supported with AngularJS Material!";
    $log.warn(msg);
  }
}

/**
 * @ngInject
 */
function MdCoreConfigure($provide, $mdThemingProvider) {

  $provide.decorator('$$rAF', ['$delegate', rAFDecorator]);
  $provide.decorator('$q', ['$delegate', qDecorator]);

  $mdThemingProvider.theme('default')
    .primaryPalette('indigo')
    .accentPalette('pink')
    .warnPalette('deep-orange')
    .backgroundPalette('grey');
}

/**
 * @ngInject
 */
function rAFDecorator($delegate) {
  /**
   * Use this to throttle events that come in often.
   * The throttled function will always use the *last* invocation before the
   * coming frame.
   *
   * For example, window resize events that fire many times a second:
   * If we set to use an raf-throttled callback on window resize, then
   * our callback will only be fired once per frame, with the last resize
   * event that happened before that frame.
   *
   * @param {function} callback function to debounce
   */
  $delegate.throttle = function(cb) {
    var queuedArgs, alreadyQueued, queueCb, context;
    return function debounced() {
      queuedArgs = arguments;
      context = this;
      queueCb = cb;
      if (!alreadyQueued) {
        alreadyQueued = true;
        $delegate(function() {
          queueCb.apply(context, Array.prototype.slice.call(queuedArgs));
          alreadyQueued = false;
        });
      }
    };
  };
  return $delegate;
}

/**
 * @ngInject
 */
function qDecorator($delegate) {
  /**
   * Adds a shim for $q.resolve for AngularJS version that don't have it,
   * so we don't have to think about it.
   *
   * via https://github.com/angular/angular.js/pull/11987
   */

  // TODO(crisbeto): this won't be necessary once we drop AngularJS 1.3
  if (!$delegate.resolve) {
    $delegate.resolve = $delegate.when;
  }
  return $delegate;
}

})();
(function(){
"use strict";


MdAutofocusDirective.$inject = ["$parse"];angular.module('material.core')
  .directive('mdAutofocus', MdAutofocusDirective)

  // Support the deprecated md-auto-focus and md-sidenav-focus as well
  .directive('mdAutoFocus', MdAutofocusDirective)
  .directive('mdSidenavFocus', MdAutofocusDirective);

/**
 * @ngdoc directive
 * @name mdAutofocus
 * @module material.core.util
 *
 * @description
 *
 * `[md-autofocus]` provides an optional way to identify the focused element when a `$mdDialog`,
 * `$mdBottomSheet`, `$mdMenu` or `$mdSidenav` opens or upon page load for input-like elements.
 *
 * When one of these opens, it will find the first nested element with the `[md-autofocus]`
 * attribute directive and optional expression. An expression may be specified as the directive
 * value to enable conditional activation of the autofocus.
 *
 * @usage
 *
 * ### Dialog
 * <hljs lang="html">
 * <md-dialog>
 *   <form>
 *     <md-input-container>
 *       <label for="testInput">Label</label>
 *       <input id="testInput" type="text" md-autofocus>
 *     </md-input-container>
 *   </form>
 * </md-dialog>
 * </hljs>
 *
 * ### Bottomsheet
 * <hljs lang="html">
 * <md-bottom-sheet class="md-list md-has-header">
 *  <md-subheader>Comment Actions</md-subheader>
 *  <md-list>
 *    <md-list-item ng-repeat="item in items">
 *
 *      <md-button md-autofocus="$index == 2">
 *        <md-icon md-svg-src="{{item.icon}}"></md-icon>
 *        <span class="md-inline-list-icon-label">{{ item.name }}</span>
 *      </md-button>
 *
 *    </md-list-item>
 *  </md-list>
 * </md-bottom-sheet>
 * </hljs>
 *
 * ### Autocomplete
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-autofocus
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 * ### Sidenav
 * <hljs lang="html">
 * <div layout="row" ng-controller="MyController">
 *   <md-sidenav md-component-id="left" class="md-sidenav-left">
 *     Left Nav!
 *   </md-sidenav>
 *
 *   <md-content>
 *     Center Content
 *     <md-button ng-click="openLeftMenu()">
 *       Open Left Menu
 *     </md-button>
 *   </md-content>
 *
 *   <md-sidenav md-component-id="right"
 *     md-is-locked-open="$mdMedia('min-width: 333px')"
 *     class="md-sidenav-right">
 *     <form>
 *       <md-input-container>
 *         <label for="testInput">Test input</label>
 *         <input id="testInput" type="text"
 *                ng-model="data" md-autofocus>
 *       </md-input-container>
 *     </form>
 *   </md-sidenav>
 * </div>
 * </hljs>
 **/
function MdAutofocusDirective($parse) {
  return {
    restrict: 'A',
    link: {
      pre: preLink
    }
  };

  function preLink(scope, element, attr) {
    var attrExp = attr.mdAutoFocus || attr.mdAutofocus || attr.mdSidenavFocus;

    // Initially update the expression by manually parsing the expression as per $watch source.
    updateExpression($parse(attrExp)(scope));

    // Only watch the expression if it is not empty.
    if (attrExp) {
      scope.$watch(attrExp, updateExpression);
    }

    /**
     * Updates the autofocus class which is used to determine whether the attribute
     * expression evaluates to true or false.
     * @param {string|boolean} value Attribute Value
     */
    function updateExpression(value) {

      // Rather than passing undefined to the jqLite toggle class function we explicitly set the
      // value to true. Otherwise the class will be just toggled instead of being forced.
      if (angular.isUndefined(value)) {
        value = true;
      }

      element.toggleClass('md-autofocus', !!value);
    }
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.colorUtil
 * @description
 * Color Util
 */
angular
  .module('material.core')
  .factory('$mdColorUtil', ColorUtilFactory);

function ColorUtilFactory() {
  /**
   * Converts hex value to RGBA string
   * @param color {string}
   * @returns {string}
   */
  function hexToRgba (color) {
    var hex   = color[ 0 ] === '#' ? color.substr(1) : color,
      dig   = hex.length / 3,
      red   = hex.substr(0, dig),
      green = hex.substr(dig, dig),
      blue  = hex.substr(dig * 2);
    if (dig === 1) {
      red += red;
      green += green;
      blue += blue;
    }
    return 'rgba(' + parseInt(red, 16) + ',' + parseInt(green, 16) + ',' + parseInt(blue, 16) + ',0.1)';
  }

  /**
   * Converts rgba value to hex string
   * @param color {string}
   * @returns {string}
   */
  function rgbaToHex(color) {
    color = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);

    var hex = (color && color.length === 4) ? "#" +
    ("0" + parseInt(color[1],10).toString(16)).slice(-2) +
    ("0" + parseInt(color[2],10).toString(16)).slice(-2) +
    ("0" + parseInt(color[3],10).toString(16)).slice(-2) : '';

    return hex.toUpperCase();
  }

  /**
   * Converts an RGB color to RGBA
   * @param color {string}
   * @returns {string}
   */
  function rgbToRgba (color) {
    return color.replace(')', ', 0.1)').replace('(', 'a(');
  }

  /**
   * Converts an RGBA color to RGB
   * @param color {string}
   * @returns {string}
   */
  function rgbaToRgb (color) {
    return color
      ? color.replace('rgba', 'rgb').replace(/,[^\),]+\)/, ')')
      : 'rgb(0,0,0)';
  }

  return {
    rgbaToHex: rgbaToHex,
    hexToRgba: hexToRgba,
    rgbToRgba: rgbToRgba,
    rgbaToRgb: rgbaToRgb
  };
}

})();
(function(){
"use strict";

angular.module('material.core')
.factory('$mdConstant', MdConstantFactory);

/**
 * Factory function that creates the grab-bag $mdConstant service.
 * @ngInject
 */
function MdConstantFactory() {

  var prefixTestEl = document.createElement('div');
  var vendorPrefix = getVendorPrefix(prefixTestEl);
  var isWebkit = /webkit/i.test(vendorPrefix);
  var SPECIAL_CHARS_REGEXP = /([:\-_]+(.))/g;

  function vendorProperty(name) {
    // Add a dash between the prefix and name, to be able to transform the string into camelcase.
    var prefixedName = vendorPrefix + '-' + name;
    var ucPrefix = camelCase(prefixedName);
    var lcPrefix = ucPrefix.charAt(0).toLowerCase() + ucPrefix.substring(1);

    return hasStyleProperty(prefixTestEl, name)     ? name     :       // The current browser supports the un-prefixed property
           hasStyleProperty(prefixTestEl, ucPrefix) ? ucPrefix :       // The current browser only supports the prefixed property.
           hasStyleProperty(prefixTestEl, lcPrefix) ? lcPrefix : name; // Some browsers are only supporting the prefix in lowercase.
  }

  function hasStyleProperty(testElement, property) {
    return angular.isDefined(testElement.style[property]);
  }

  function camelCase(input) {
    return input.replace(SPECIAL_CHARS_REGEXP, function(matches, separator, letter, offset) {
      return offset ? letter.toUpperCase() : letter;
    });
  }

  function getVendorPrefix(testElement) {
    var prop, match;
    var vendorRegex = /^(Moz|webkit|ms)(?=[A-Z])/;

    for (prop in testElement.style) {
      if (match = vendorRegex.exec(prop)) {
        return match[0];
      }
    }
  }

  var self = {
    isInputKey : function(e) { return (e.keyCode >= 31 && e.keyCode <= 90); },
    isNumPadKey : function(e) { return (3 === e.location && e.keyCode >= 97 && e.keyCode <= 105); },
    isMetaKey: function(e) { return (e.keyCode >= 91 && e.keyCode <= 93); },
    isFnLockKey: function(e) { return (e.keyCode >= 112 && e.keyCode <= 145); },
    isNavigationKey : function(e) {
      var kc = self.KEY_CODE, NAVIGATION_KEYS =  [kc.SPACE, kc.ENTER, kc.UP_ARROW, kc.DOWN_ARROW];
      return (NAVIGATION_KEYS.indexOf(e.keyCode) != -1);
    },
    hasModifierKey: function(e) {
      return e.ctrlKey || e.metaKey || e.altKey;
    },

    /**
     * Maximum size, in pixels, that can be explicitly set to an element. The actual value varies
     * between browsers, but IE11 has the very lowest size at a mere 1,533,917px. Ideally we could
     * compute this value, but Firefox always reports an element to have a size of zero if it
     * goes over the max, meaning that we'd have to binary search for the value.
     */
    ELEMENT_MAX_PIXELS: 1533917,

    /**
     * Priority for a directive that should run before the directives from ngAria.
     */
    BEFORE_NG_ARIA: 210,

    /**
     * Common Keyboard actions and their associated keycode.
     */
    KEY_CODE: {
      COMMA: 188,
      SEMICOLON : 186,
      ENTER: 13,
      ESCAPE: 27,
      SPACE: 32,
      PAGE_UP: 33,
      PAGE_DOWN: 34,
      END: 35,
      HOME: 36,
      LEFT_ARROW : 37,
      UP_ARROW : 38,
      RIGHT_ARROW : 39,
      DOWN_ARROW : 40,
      TAB : 9,
      BACKSPACE: 8,
      DELETE: 46
    },

    /**
     * Vendor prefixed CSS properties to be used to support the given functionality in older browsers
     * as well.
     */
    CSS: {
      /* Constants */
      TRANSITIONEND: 'transitionend' + (isWebkit ? ' webkitTransitionEnd' : ''),
      ANIMATIONEND: 'animationend' + (isWebkit ? ' webkitAnimationEnd' : ''),

      TRANSFORM: vendorProperty('transform'),
      TRANSFORM_ORIGIN: vendorProperty('transformOrigin'),
      TRANSITION: vendorProperty('transition'),
      TRANSITION_DURATION: vendorProperty('transitionDuration'),
      ANIMATION_PLAY_STATE: vendorProperty('animationPlayState'),
      ANIMATION_DURATION: vendorProperty('animationDuration'),
      ANIMATION_NAME: vendorProperty('animationName'),
      ANIMATION_TIMING: vendorProperty('animationTimingFunction'),
      ANIMATION_DIRECTION: vendorProperty('animationDirection')
    },

    /**
     * As defined in core/style/variables.scss
     *
     * $layout-breakpoint-xs:     600px !default;
     * $layout-breakpoint-sm:     960px !default;
     * $layout-breakpoint-md:     1280px !default;
     * $layout-breakpoint-lg:     1920px !default;
     *
     */
    MEDIA: {
      'xs'        : '(max-width: 599px)'                         ,
      'gt-xs'     : '(min-width: 600px)'                         ,
      'sm'        : '(min-width: 600px) and (max-width: 959px)'  ,
      'gt-sm'     : '(min-width: 960px)'                         ,
      'md'        : '(min-width: 960px) and (max-width: 1279px)' ,
      'gt-md'     : '(min-width: 1280px)'                        ,
      'lg'        : '(min-width: 1280px) and (max-width: 1919px)',
      'gt-lg'     : '(min-width: 1920px)'                        ,
      'xl'        : '(min-width: 1920px)'                        ,
      'landscape' : '(orientation: landscape)'                   ,
      'portrait'  : '(orientation: portrait)'                    ,
      'print' : 'print'
    },

    MEDIA_PRIORITY: [
      'xl',
      'gt-lg',
      'lg',
      'gt-md',
      'md',
      'gt-sm',
      'sm',
      'gt-xs',
      'xs',
      'landscape',
      'portrait',
      'print'
    ]
  };

  return self;
}

})();
(function(){
"use strict";

  angular
    .module('material.core')
    .config( ["$provide", function($provide){
       $provide.decorator('$mdUtil', ['$delegate', function ($delegate){
           /**
            * Inject the iterator facade to easily support iteration and accessors
            * @see iterator below
            */
           $delegate.iterator = MdIterator;

           return $delegate;
         }
       ]);
     }]);

  /**
   * iterator is a list facade to easily support iteration and accessors
   *
   * @param items Array list which this iterator will enumerate
   * @param reloop Boolean enables iterator to consider the list as an endless reloop
   */
  function MdIterator(items, reloop) {
    var trueFn = function() { return true; };

    if (items && !angular.isArray(items)) {
      items = Array.prototype.slice.call(items);
    }

    reloop = !!reloop;
    var _items = items || [ ];

    // Published API
    return {
      items: getItems,
      count: count,

      inRange: inRange,
      contains: contains,
      indexOf: indexOf,
      itemAt: itemAt,

      findBy: findBy,

      add: add,
      remove: remove,

      first: first,
      last: last,
      next: angular.bind(null, findSubsequentItem, false),
      previous: angular.bind(null, findSubsequentItem, true),

      hasPrevious: hasPrevious,
      hasNext: hasNext

    };

    /**
     * Publish copy of the enumerable set
     * @returns {Array|*}
     */
    function getItems() {
      return [].concat(_items);
    }

    /**
     * Determine length of the list
     * @returns {Array.length|*|number}
     */
    function count() {
      return _items.length;
    }

    /**
     * Is the index specified valid
     * @param index
     * @returns {Array.length|*|number|boolean}
     */
    function inRange(index) {
      return _items.length && ( index > -1 ) && (index < _items.length );
    }

    /**
     * Can the iterator proceed to the next item in the list; relative to
     * the specified item.
     *
     * @param item
     * @returns {Array.length|*|number|boolean}
     */
    function hasNext(item) {
      return item ? inRange(indexOf(item) + 1) : false;
    }

    /**
     * Can the iterator proceed to the previous item in the list; relative to
     * the specified item.
     *
     * @param item
     * @returns {Array.length|*|number|boolean}
     */
    function hasPrevious(item) {
      return item ? inRange(indexOf(item) - 1) : false;
    }

    /**
     * Get item at specified index/position
     * @param index
     * @returns {*}
     */
    function itemAt(index) {
      return inRange(index) ? _items[index] : null;
    }

    /**
     * Find all elements matching the key/value pair
     * otherwise return null
     *
     * @param val
     * @param key
     *
     * @return array
     */
    function findBy(key, val) {
      return _items.filter(function(item) {
        return item[key] === val;
      });
    }

    /**
     * Add item to list
     * @param item
     * @param index
     * @returns {*}
     */
    function add(item, index) {
      if ( !item ) return -1;

      if (!angular.isNumber(index)) {
        index = _items.length;
      }

      _items.splice(index, 0, item);

      return indexOf(item);
    }

    /**
     * Remove item from list...
     * @param item
     */
    function remove(item) {
      if ( contains(item) ){
        _items.splice(indexOf(item), 1);
      }
    }

    /**
     * Get the zero-based index of the target item
     * @param item
     * @returns {*}
     */
    function indexOf(item) {
      return _items.indexOf(item);
    }

    /**
     * Boolean existence check
     * @param item
     * @returns {boolean}
     */
    function contains(item) {
      return item && (indexOf(item) > -1);
    }

    /**
     * Return first item in the list
     * @returns {*}
     */
    function first() {
      return _items.length ? _items[0] : null;
    }

    /**
     * Return last item in the list...
     * @returns {*}
     */
    function last() {
      return _items.length ? _items[_items.length - 1] : null;
    }

    /**
     * Find the next item. If reloop is true and at the end of the list, it will go back to the
     * first item. If given, the `validate` callback will be used to determine whether the next item
     * is valid. If not valid, it will try to find the next item again.
     *
     * @param {boolean} backwards Specifies the direction of searching (forwards/backwards)
     * @param {*} item The item whose subsequent item we are looking for
     * @param {Function=} validate The `validate` function
     * @param {integer=} limit The recursion limit
     *
     * @returns {*} The subsequent item or null
     */
    function findSubsequentItem(backwards, item, validate, limit) {
      validate = validate || trueFn;

      var curIndex = indexOf(item);
      while (true) {
        if (!inRange(curIndex)) return null;

        var nextIndex = curIndex + (backwards ? -1 : 1);
        var foundItem = null;
        if (inRange(nextIndex)) {
          foundItem = _items[nextIndex];
        } else if (reloop) {
          foundItem = backwards ? last() : first();
          nextIndex = indexOf(foundItem);
        }

        if ((foundItem === null) || (nextIndex === limit)) return null;
        if (validate(foundItem)) return foundItem;

        if (angular.isUndefined(limit)) limit = nextIndex;

        curIndex = nextIndex;
      }
    }
  }


})();
(function(){
"use strict";


mdMediaFactory.$inject = ["$mdConstant", "$rootScope", "$window"];angular.module('material.core')
.factory('$mdMedia', mdMediaFactory);

/**
 * @ngdoc service
 * @name $mdMedia
 * @module material.core
 *
 * @description
 * `$mdMedia` is used to evaluate whether a given media query is true or false given the
 * current device's screen / window size. The media query will be re-evaluated on resize, allowing
 * you to register a watch.
 *
 * `$mdMedia` also has pre-programmed support for media queries that match the layout breakpoints:
 *
 *  <table class="md-api-table">
 *    <thead>
 *    <tr>
 *      <th>Breakpoint</th>
 *      <th>mediaQuery</th>
 *    </tr>
 *    </thead>
 *    <tbody>
 *    <tr>
 *      <td>xs</td>
 *      <td>(max-width: 599px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-xs</td>
 *      <td>(min-width: 600px)</td>
 *    </tr>
 *    <tr>
 *      <td>sm</td>
 *      <td>(min-width: 600px) and (max-width: 959px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-sm</td>
 *      <td>(min-width: 960px)</td>
 *    </tr>
 *    <tr>
 *      <td>md</td>
 *      <td>(min-width: 960px) and (max-width: 1279px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-md</td>
 *      <td>(min-width: 1280px)</td>
 *    </tr>
 *    <tr>
 *      <td>lg</td>
 *      <td>(min-width: 1280px) and (max-width: 1919px)</td>
 *    </tr>
 *    <tr>
 *      <td>gt-lg</td>
 *      <td>(min-width: 1920px)</td>
 *    </tr>
 *    <tr>
 *      <td>xl</td>
 *      <td>(min-width: 1920px)</td>
 *    </tr>
 *    <tr>
 *      <td>landscape</td>
 *      <td>landscape</td>
 *    </tr>
 *    <tr>
 *      <td>portrait</td>
 *      <td>portrait</td>
 *    </tr>
 *    <tr>
 *      <td>print</td>
 *      <td>print</td>
 *    </tr>
 *    </tbody>
 *  </table>
 *
 *  See Material Design's <a href="https://material.google.com/layout/responsive-ui.html">Layout - Adaptive UI</a> for more details.
 *
 *  <a href="https://www.google.com/design/spec/layout/adaptive-ui.html">
 *  <img src="https://material-design.storage.googleapis.com/publish/material_v_4/material_ext_publish/0B8olV15J7abPSGFxemFiQVRtb1k/layout_adaptive_breakpoints_01.png" width="100%" height="100%"></img>
 *  </a>
 *
 * @returns {boolean} a boolean representing whether or not the given media query is true or false.
 *
 * @usage
 * <hljs lang="js">
 * app.controller('MyController', function($mdMedia, $scope) {
 *   $scope.$watch(function() { return $mdMedia('lg'); }, function(big) {
 *     $scope.bigScreen = big;
 *   });
 *
 *   $scope.screenIsSmall = $mdMedia('sm');
 *   $scope.customQuery = $mdMedia('(min-width: 1234px)');
 *   $scope.anotherCustom = $mdMedia('max-width: 300px');
 * });
 * </hljs>
 */

/* @ngInject */
function mdMediaFactory($mdConstant, $rootScope, $window) {
  var queries = {};
  var mqls = {};
  var results = {};
  var normalizeCache = {};

  $mdMedia.getResponsiveAttribute = getResponsiveAttribute;
  $mdMedia.getQuery = getQuery;
  $mdMedia.watchResponsiveAttributes = watchResponsiveAttributes;

  return $mdMedia;

  function $mdMedia(query) {
    var validated = queries[query];
    if (angular.isUndefined(validated)) {
      validated = queries[query] = validate(query);
    }

    var result = results[validated];
    if (angular.isUndefined(result)) {
      result = add(validated);
    }

    return result;
  }

  function validate(query) {
    return $mdConstant.MEDIA[query] ||
           ((query.charAt(0) !== '(') ? ('(' + query + ')') : query);
  }

  function add(query) {
    var result = mqls[query];
    if ( !result ) {
      result = mqls[query] = $window.matchMedia(query);
    }

    result.addListener(onQueryChange);
    return (results[result.media] = !!result.matches);
  }

  function onQueryChange(query) {
    $rootScope.$evalAsync(function() {
      results[query.media] = !!query.matches;
    });
  }

  function getQuery(name) {
    return mqls[name];
  }

  function getResponsiveAttribute(attrs, attrName) {
    for (var i = 0; i < $mdConstant.MEDIA_PRIORITY.length; i++) {
      var mediaName = $mdConstant.MEDIA_PRIORITY[i];
      if (!mqls[queries[mediaName]].matches) {
        continue;
      }

      var normalizedName = getNormalizedName(attrs, attrName + '-' + mediaName);
      if (attrs[normalizedName]) {
        return attrs[normalizedName];
      }
    }

    // fallback on unprefixed
    return attrs[getNormalizedName(attrs, attrName)];
  }

  function watchResponsiveAttributes(attrNames, attrs, watchFn) {
    var unwatchFns = [];
    attrNames.forEach(function(attrName) {
      var normalizedName = getNormalizedName(attrs, attrName);
      if (angular.isDefined(attrs[normalizedName])) {
        unwatchFns.push(
            attrs.$observe(normalizedName, angular.bind(void 0, watchFn, null)));
      }

      for (var mediaName in $mdConstant.MEDIA) {
        normalizedName = getNormalizedName(attrs, attrName + '-' + mediaName);
        if (angular.isDefined(attrs[normalizedName])) {
          unwatchFns.push(
              attrs.$observe(normalizedName, angular.bind(void 0, watchFn, mediaName)));
        }
      }
    });

    return function unwatch() {
      unwatchFns.forEach(function(fn) { fn(); });
    };
  }

  // Improves performance dramatically
  function getNormalizedName(attrs, attrName) {
    return normalizeCache[attrName] ||
        (normalizeCache[attrName] = attrs.$normalize(attrName));
  }
}

})();
(function(){
"use strict";

angular
  .module('material.core')
  .config( ["$provide", function($provide) {
    $provide.decorator('$mdUtil', ['$delegate', function ($delegate) {

      // Inject the prefixer into our original $mdUtil service.
      $delegate.prefixer = MdPrefixer;

      return $delegate;
    }]);
  }]);

function MdPrefixer(initialAttributes, buildSelector) {
  var PREFIXES = ['data', 'x'];

  if (initialAttributes) {
    // The prefixer also accepts attributes as a parameter, and immediately builds a list or selector for
    // the specified attributes.
    return buildSelector ? _buildSelector(initialAttributes) : _buildList(initialAttributes);
  }

  return {
    buildList: _buildList,
    buildSelector: _buildSelector,
    hasAttribute: _hasAttribute,
    removeAttribute: _removeAttribute
  };

  function _buildList(attributes) {
    attributes = angular.isArray(attributes) ? attributes : [attributes];

    attributes.forEach(function(item) {
      PREFIXES.forEach(function(prefix) {
        attributes.push(prefix + '-' + item);
      });
    });

    return attributes;
  }

  function _buildSelector(attributes) {
    attributes = angular.isArray(attributes) ? attributes : [attributes];

    return _buildList(attributes)
      .map(function(item) {
        return '[' + item + ']';
      })
      .join(',');
  }

  function _hasAttribute(element, attribute) {
    element = _getNativeElement(element);

    if (!element) {
      return false;
    }

    var prefixedAttrs = _buildList(attribute);

    for (var i = 0; i < prefixedAttrs.length; i++) {
      if (element.hasAttribute(prefixedAttrs[i])) {
        return true;
      }
    }

    return false;
  }

  function _removeAttribute(element, attribute) {
    element = _getNativeElement(element);

    if (!element) {
      return;
    }

    _buildList(attribute).forEach(function(prefixedAttribute) {
      element.removeAttribute(prefixedAttribute);
    });
  }

  /**
   * Transforms a jqLite or DOM element into a HTML element.
   * This is useful when supporting jqLite elements and DOM elements at
   * same time.
   * @param element {JQLite|Element} Element to be parsed
   * @returns {HTMLElement} Parsed HTMLElement
   */
  function _getNativeElement(element) {
    element =  element[0] || element;

    if (element.nodeType) {
      return element;
    }
  }

}

})();
(function(){
"use strict";

/*
 * This var has to be outside the angular factory, otherwise when
 * there are multiple material apps on the same page, each app
 * will create its own instance of this array and the app's IDs
 * will not be unique.
 */
UtilFactory.$inject = ["$document", "$timeout", "$compile", "$rootScope", "$$mdAnimate", "$interpolate", "$log", "$rootElement", "$window", "$$rAF"];
var nextUniqueId = 0;

/**
 * @ngdoc module
 * @name material.core.util
 * @description
 * Util
 */
angular
  .module('material.core')
  .factory('$mdUtil', UtilFactory);

/**
 * @ngInject
 */
function UtilFactory($document, $timeout, $compile, $rootScope, $$mdAnimate, $interpolate, $log, $rootElement, $window, $$rAF) {
  // Setup some core variables for the processTemplate method
  var startSymbol = $interpolate.startSymbol(),
    endSymbol = $interpolate.endSymbol(),
    usesStandardSymbols = ((startSymbol === '{{') && (endSymbol === '}}'));

  /**
   * Checks if the target element has the requested style by key
   * @param {DOMElement|JQLite} target Target element
   * @param {string} key Style key
   * @param {string=} expectedVal Optional expected value
   * @returns {boolean} Whether the target element has the style or not
   */
  var hasComputedStyle = function (target, key, expectedVal) {
    var hasValue = false;

    if ( target && target.length  ) {
      var computedStyles = $window.getComputedStyle(target[0]);
      hasValue = angular.isDefined(computedStyles[key]) && (expectedVal ? computedStyles[key] == expectedVal : true);
    }

    return hasValue;
  };

  function validateCssValue(value) {
    return !value       ? '0'   :
      hasPx(value) || hasPercent(value) ? value : value + 'px';
  }

  function hasPx(value) {
    return String(value).indexOf('px') > -1;
  }

  function hasPercent(value) {
    return String(value).indexOf('%') > -1;

  }

  var $mdUtil = {
    dom: {},
    now: window.performance && window.performance.now ?
      angular.bind(window.performance, window.performance.now) : Date.now || function() {
      return new Date().getTime();
    },

    /**
     * Cross-version compatibility method to retrieve an option of a ngModel controller,
     * which supports the breaking changes in the AngularJS snapshot (SHA 87a2ff76af5d0a9268d8eb84db5755077d27c84c).
     * @param {!angular.ngModelCtrl} ngModelCtrl
     * @param {!string} optionName
     * @returns {Object|undefined}
     */
    getModelOption: function (ngModelCtrl, optionName) {
      if (!ngModelCtrl.$options) {
        return;
      }

      var $options = ngModelCtrl.$options;

      // The newer versions of AngularJS introduced a `getOption function and made the option values no longer
      // visible on the $options object.
      return $options.getOption ? $options.getOption(optionName) : $options[optionName]
    },

    /**
     * Bi-directional accessor/mutator used to easily update an element's
     * property based on the current 'dir'ectional value.
     */
    bidi : function(element, property, lValue, rValue) {
      var ltr = !($document[0].dir == 'rtl' || $document[0].body.dir == 'rtl');

      // If accessor
      if ( arguments.length == 0 ) return ltr ? 'ltr' : 'rtl';

      // If mutator
      var elem = angular.element(element);

      if ( ltr && angular.isDefined(lValue)) {
        elem.css(property, validateCssValue(lValue));
      }
      else if ( !ltr && angular.isDefined(rValue)) {
        elem.css(property, validateCssValue(rValue) );
      }
    },

    bidiProperty: function (element, lProperty, rProperty, value) {
      var ltr = !($document[0].dir == 'rtl' || $document[0].body.dir == 'rtl');

      var elem = angular.element(element);

      if ( ltr && angular.isDefined(lProperty)) {
        elem.css(lProperty, validateCssValue(value));
        elem.css(rProperty, '');
      }
      else if ( !ltr && angular.isDefined(rProperty)) {
        elem.css(rProperty, validateCssValue(value) );
        elem.css(lProperty, '');
      }
    },

    clientRect: function(element, offsetParent, isOffsetRect) {
      var node = getNode(element);
      offsetParent = getNode(offsetParent || node.offsetParent || document.body);
      var nodeRect = node.getBoundingClientRect();

      // The user can ask for an offsetRect: a rect relative to the offsetParent,
      // or a clientRect: a rect relative to the page
      var offsetRect = isOffsetRect ?
        offsetParent.getBoundingClientRect() :
      {left: 0, top: 0, width: 0, height: 0};
      return {
        left: nodeRect.left - offsetRect.left,
        top: nodeRect.top - offsetRect.top,
        width: nodeRect.width,
        height: nodeRect.height
      };
    },
    offsetRect: function(element, offsetParent) {
      return $mdUtil.clientRect(element, offsetParent, true);
    },

    // Annoying method to copy nodes to an array, thanks to IE
    nodesToArray: function(nodes) {
      nodes = nodes || [];

      var results = [];
      for (var i = 0; i < nodes.length; ++i) {
        results.push(nodes.item(i));
      }
      return results;
    },

    /**
     * Determines the absolute position of the viewport.
     * Useful when making client rectangles absolute.
     * @returns {number}
     */
    getViewportTop: function() {
      return window.scrollY || window.pageYOffset || 0;
    },

    /**
     * Finds the proper focus target by searching the DOM.
     *
     * @param containerEl
     * @param attributeVal
     * @returns {*}
     */
    findFocusTarget: function(containerEl, attributeVal) {
      var AUTO_FOCUS = this.prefixer('md-autofocus', true);
      var elToFocus;

      elToFocus = scanForFocusable(containerEl, attributeVal || AUTO_FOCUS);

      if ( !elToFocus && attributeVal != AUTO_FOCUS) {
        // Scan for deprecated attribute
        elToFocus = scanForFocusable(containerEl, this.prefixer('md-auto-focus', true));

        if ( !elToFocus ) {
          // Scan for fallback to 'universal' API
          elToFocus = scanForFocusable(containerEl, AUTO_FOCUS);
        }
      }

      return elToFocus;

      /**
       * Can target and nested children for specified Selector (attribute)
       * whose value may be an expression that evaluates to True/False.
       */
      function scanForFocusable(target, selector) {
        var elFound, items = target[0].querySelectorAll(selector);

        // Find the last child element with the focus attribute
        if ( items && items.length ){
          items.length && angular.forEach(items, function(it) {
            it = angular.element(it);

            // Check the element for the md-autofocus class to ensure any associated expression
            // evaluated to true.
            var isFocusable = it.hasClass('md-autofocus');
            if (isFocusable) elFound = it;
          });
        }
        return elFound;
      }
    },

    /**
     * Disables scroll around the passed parent element.
     * @param element Unused
     * @param {!Element|!angular.JQLite} parent Element to disable scrolling within.
     *   Defaults to body if none supplied.
     * @param options Object of options to modify functionality
     *   - disableScrollMask Boolean of whether or not to create a scroll mask element or
     *     use the passed parent element.
     */
    disableScrollAround: function(element, parent, options) {
      options = options || {};

      $mdUtil.disableScrollAround._count = Math.max(0, $mdUtil.disableScrollAround._count || 0);
      $mdUtil.disableScrollAround._count++;

      if ($mdUtil.disableScrollAround._restoreScroll) {
        return $mdUtil.disableScrollAround._restoreScroll;
      }

      var body = $document[0].body;
      var restoreBody = disableBodyScroll();
      var restoreElement = disableElementScroll(parent);

      return $mdUtil.disableScrollAround._restoreScroll = function() {
        if (--$mdUtil.disableScrollAround._count <= 0) {
          restoreBody();
          restoreElement();
          delete $mdUtil.disableScrollAround._restoreScroll;
        }
      };

      /**
       * Creates a virtual scrolling mask to prevent touchmove, keyboard, scrollbar clicking,
       * and wheel events
       */
      function disableElementScroll(element) {
        element = angular.element(element || body);

        var scrollMask;

        if (options.disableScrollMask) {
          scrollMask = element;
        } else {
          scrollMask = angular.element(
            '<div class="md-scroll-mask">' +
            '  <div class="md-scroll-mask-bar"></div>' +
            '</div>');
          element.append(scrollMask);
        }

        scrollMask.on('wheel', preventDefault);
        scrollMask.on('touchmove', preventDefault);

        return function restoreElementScroll() {
          scrollMask.off('wheel');
          scrollMask.off('touchmove');

          if (!options.disableScrollMask && scrollMask[0].parentNode ) {
            scrollMask[0].parentNode.removeChild(scrollMask[0]);
          }
        };

        function preventDefault(e) {
          e.preventDefault();
        }
      }

      // Converts the body to a position fixed block and translate it to the proper scroll position
      function disableBodyScroll() {
        var documentElement = $document[0].documentElement;

        var prevDocumentStyle = documentElement.style.cssText || '';
        var prevBodyStyle = body.style.cssText || '';

        var viewportTop = $mdUtil.getViewportTop();
        var clientWidth = body.clientWidth;
        var hasVerticalScrollbar = body.scrollHeight > body.clientHeight + 1;

        // Scroll may be set on <html> element (for example by overflow-y: scroll)
        // but Chrome is reporting the scrollTop position always on <body>.
        // scrollElement will allow to restore the scrollTop position to proper target.
        var scrollElement = documentElement.scrollTop > 0 ? documentElement : body;

        if (hasVerticalScrollbar) {
          angular.element(body).css({
            position: 'fixed',
            width: '100%',
            top: -viewportTop + 'px'
          });
        }

        if (body.clientWidth < clientWidth) {
          body.style.overflow = 'hidden';
        }

        // This should be applied after the manipulation to the body, because
        // adding a scrollbar can potentially resize it, causing the measurement
        // to change.
        if (hasVerticalScrollbar) {
          documentElement.style.overflowY = 'scroll';
        }

        return function restoreScroll() {
          // Reset the inline style CSS to the previous.
          body.style.cssText = prevBodyStyle;
          documentElement.style.cssText = prevDocumentStyle;

          // The scroll position while being fixed
          scrollElement.scrollTop = viewportTop;
        };
      }

    },

    enableScrolling: function() {
      var restoreFn = this.disableScrollAround._restoreScroll;
      restoreFn && restoreFn();
    },

    floatingScrollbars: function() {
      if (this.floatingScrollbars.cached === undefined) {
        var tempNode = angular.element('<div><div></div></div>').css({
          width: '100%',
          'z-index': -1,
          position: 'absolute',
          height: '35px',
          'overflow-y': 'scroll'
        });
        tempNode.children().css('height', '60px');

        $document[0].body.appendChild(tempNode[0]);
        this.floatingScrollbars.cached = (tempNode[0].offsetWidth == tempNode[0].childNodes[0].offsetWidth);
        tempNode.remove();
      }
      return this.floatingScrollbars.cached;
    },

    // Mobile safari only allows you to set focus in click event listeners...
    forceFocus: function(element) {
      var node = element[0] || element;

      document.addEventListener('click', function focusOnClick(ev) {
        if (ev.target === node && ev.$focus) {
          node.focus();
          ev.stopImmediatePropagation();
          ev.preventDefault();
          node.removeEventListener('click', focusOnClick);
        }
      }, true);

      var newEvent = document.createEvent('MouseEvents');
      newEvent.initMouseEvent('click', false, true, window, {}, 0, 0, 0, 0,
        false, false, false, false, 0, null);
      newEvent.$material = true;
      newEvent.$focus = true;
      node.dispatchEvent(newEvent);
    },

    /**
     * facade to build md-backdrop element with desired styles
     * NOTE: Use $compile to trigger backdrop postLink function
     */
    createBackdrop: function(scope, addClass) {
      return $compile($mdUtil.supplant('<md-backdrop class="{0}">', [addClass]))(scope);
    },

    /**
     * supplant() method from Crockford's `Remedial Javascript`
     * Equivalent to use of $interpolate; without dependency on
     * interpolation symbols and scope. Note: the '{<token>}' can
     * be property names, property chains, or array indices.
     */
    supplant: function(template, values, pattern) {
      pattern = pattern || /\{([^\{\}]*)\}/g;
      return template.replace(pattern, function(a, b) {
        var p = b.split('.'),
          r = values;
        try {
          for (var s in p) {
            if (p.hasOwnProperty(s) ) {
              r = r[p[s]];
            }
          }
        } catch (e) {
          r = a;
        }
        return (typeof r === 'string' || typeof r === 'number') ? r : a;
      });
    },

    fakeNgModel: function() {
      return {
        $fake: true,
        $setTouched: angular.noop,
        $setViewValue: function(value) {
          this.$viewValue = value;
          this.$render(value);
          this.$viewChangeListeners.forEach(function(cb) {
            cb();
          });
        },
        $isEmpty: function(value) {
          return ('' + value).length === 0;
        },
        $parsers: [],
        $formatters: [],
        $viewChangeListeners: [],
        $render: angular.noop
      };
    },

    // Returns a function, that, as long as it continues to be invoked, will not
    // be triggered. The function will be called after it stops being called for
    // N milliseconds.
    // @param wait Integer value of msecs to delay (since last debounce reset); default value 10 msecs
    // @param invokeApply should the $timeout trigger $digest() dirty checking
    debounce: function(func, wait, scope, invokeApply) {
      var timer;

      return function debounced() {
        var context = scope,
          args = Array.prototype.slice.call(arguments);

        $timeout.cancel(timer);
        timer = $timeout(function() {

          timer = undefined;
          func.apply(context, args);

        }, wait || 10, invokeApply);
      };
    },

    // Returns a function that can only be triggered every `delay` milliseconds.
    // In other words, the function will not be called unless it has been more
    // than `delay` milliseconds since the last call.
    throttle: function throttle(func, delay) {
      var recent;
      return function throttled() {
        var context = this;
        var args = arguments;
        var now = $mdUtil.now();

        if (!recent || (now - recent > delay)) {
          func.apply(context, args);
          recent = now;
        }
      };
    },

    /**
     * Measures the number of milliseconds taken to run the provided callback
     * function. Uses a high-precision timer if available.
     */
    time: function time(cb) {
      var start = $mdUtil.now();
      cb();
      return $mdUtil.now() - start;
    },

    /**
     * Create an implicit getter that caches its `getter()`
     * lookup value
     */
    valueOnUse : function (scope, key, getter) {
      var value = null, args = Array.prototype.slice.call(arguments);
      var params = (args.length > 3) ? args.slice(3) : [ ];

      Object.defineProperty(scope, key, {
        get: function () {
          if (value === null) value = getter.apply(scope, params);
          return value;
        }
      });
    },

    /**
     * Get a unique ID.
     *
     * @returns {string} an unique numeric string
     */
    nextUid: function() {
      return '' + nextUniqueId++;
    },

    // Stop watchers and events from firing on a scope without destroying it,
    // by disconnecting it from its parent and its siblings' linked lists.
    disconnectScope: function disconnectScope(scope) {
      if (!scope) return;

      // we can't destroy the root scope or a scope that has been already destroyed
      if (scope.$root === scope) return;
      if (scope.$$destroyed) return;

      var parent = scope.$parent;
      scope.$$disconnected = true;

      // See Scope.$destroy
      if (parent.$$childHead === scope) parent.$$childHead = scope.$$nextSibling;
      if (parent.$$childTail === scope) parent.$$childTail = scope.$$prevSibling;
      if (scope.$$prevSibling) scope.$$prevSibling.$$nextSibling = scope.$$nextSibling;
      if (scope.$$nextSibling) scope.$$nextSibling.$$prevSibling = scope.$$prevSibling;

      scope.$$nextSibling = scope.$$prevSibling = null;

    },

    // Undo the effects of disconnectScope above.
    reconnectScope: function reconnectScope(scope) {
      if (!scope) return;

      // we can't disconnect the root node or scope already disconnected
      if (scope.$root === scope) return;
      if (!scope.$$disconnected) return;

      var child = scope;

      var parent = child.$parent;
      child.$$disconnected = false;
      // See Scope.$new for this logic...
      child.$$prevSibling = parent.$$childTail;
      if (parent.$$childHead) {
        parent.$$childTail.$$nextSibling = child;
        parent.$$childTail = child;
      } else {
        parent.$$childHead = parent.$$childTail = child;
      }
    },

    /*
     * getClosest replicates jQuery.closest() to walk up the DOM tree until it finds a matching nodeName
     *
     * @param el Element to start walking the DOM from
     * @param check Either a string or a function. If a string is passed, it will be evaluated against
     * each of the parent nodes' tag name. If a function is passed, the loop will call it with each of
     * the parents and will use the return value to determine whether the node is a match.
     * @param onlyParent Only start checking from the parent element, not `el`.
     */
    getClosest: function getClosest(el, validateWith, onlyParent) {
      if ( angular.isString(validateWith) ) {
        var tagName = validateWith.toUpperCase();
        validateWith = function(el) {
          return el.nodeName.toUpperCase() === tagName;
        };
      }

      if (el instanceof angular.element) el = el[0];
      if (onlyParent) el = el.parentNode;
      if (!el) return null;

      do {
        if (validateWith(el)) {
          return el;
        }
      } while (el = el.parentNode);

      return null;
    },

    /**
     * Build polyfill for the Node.contains feature (if needed)
     */
    elementContains: function(node, child) {
      var hasContains = (window.Node && window.Node.prototype && Node.prototype.contains);
      var findFn = hasContains ? angular.bind(node, node.contains) : angular.bind(node, function(arg) {
        // compares the positions of two nodes and returns a bitmask
        return (node === child) || !!(this.compareDocumentPosition(arg) & 16)
      });

      return findFn(child);
    },

    /**
     * Functional equivalent for $element.filter(‘md-bottom-sheet’)
     * useful with interimElements where the element and its container are important...
     *
     * @param {[]} elements to scan
     * @param {string} name of node to find (e.g. 'md-dialog')
     * @param {boolean=} optional flag to allow deep scans; defaults to 'false'.
     * @param {boolean=} optional flag to enable log warnings; defaults to false
     */
    extractElementByName: function(element, nodeName, scanDeep, warnNotFound) {
      var found = scanTree(element);
      if (!found && !!warnNotFound) {
        $log.warn( $mdUtil.supplant("Unable to find node '{0}' in element '{1}'.",[nodeName, element[0].outerHTML]) );
      }

      return angular.element(found || element);

      /**
       * Breadth-First tree scan for element with matching `nodeName`
       */
      function scanTree(element) {
        return scanLevel(element) || (!!scanDeep ? scanChildren(element) : null);
      }

      /**
       * Case-insensitive scan of current elements only (do not descend).
       */
      function scanLevel(element) {
        if ( element ) {
          for (var i = 0, len = element.length; i < len; i++) {
            if (element[i].nodeName.toLowerCase() === nodeName) {
              return element[i];
            }
          }
        }
        return null;
      }

      /**
       * Scan children of specified node
       */
      function scanChildren(element) {
        var found;
        if ( element ) {
          for (var i = 0, len = element.length; i < len; i++) {
            var target = element[i];
            if ( !found ) {
              for (var j = 0, numChild = target.childNodes.length; j < numChild; j++) {
                found = found || scanTree([target.childNodes[j]]);
              }
            }
          }
        }
        return found;
      }

    },

    /**
     * Give optional properties with no value a boolean true if attr provided or false otherwise
     */
    initOptionalProperties: function(scope, attr, defaults) {
      defaults = defaults || {};
      angular.forEach(scope.$$isolateBindings, function(binding, key) {
        if (binding.optional && angular.isUndefined(scope[key])) {
          var attrIsDefined = angular.isDefined(attr[binding.attrName]);
          scope[key] = angular.isDefined(defaults[key]) ? defaults[key] : attrIsDefined;
        }
      });
    },

    /**
     * Alternative to $timeout calls with 0 delay.
     * nextTick() coalesces all calls within a single frame
     * to minimize $digest thrashing
     *
     * @param callback
     * @param digest
     * @returns {*}
     */
    nextTick: function(callback, digest, scope) {
      //-- grab function reference for storing state details
      var nextTick = $mdUtil.nextTick;
      var timeout = nextTick.timeout;
      var queue = nextTick.queue || [];

      //-- add callback to the queue
      queue.push({scope: scope, callback: callback});

      //-- set default value for digest
      if (digest == null) digest = true;

      //-- store updated digest/queue values
      nextTick.digest = nextTick.digest || digest;
      nextTick.queue = queue;

      //-- either return existing timeout or create a new one
      return timeout || (nextTick.timeout = $timeout(processQueue, 0, false));

      /**
       * Grab a copy of the current queue
       * Clear the queue for future use
       * Process the existing queue
       * Trigger digest if necessary
       */
      function processQueue() {
        var queue = nextTick.queue;
        var digest = nextTick.digest;

        nextTick.queue = [];
        nextTick.timeout = null;
        nextTick.digest = false;

        queue.forEach(function(queueItem) {
          var skip = queueItem.scope && queueItem.scope.$$destroyed;
          if (!skip) {
            queueItem.callback();
          }
        });

        if (digest) $rootScope.$digest();
      }
    },

    /**
     * Processes a template and replaces the start/end symbols if the application has
     * overriden them.
     *
     * @param template The template to process whose start/end tags may be replaced.
     * @returns {*}
     */
    processTemplate: function(template) {
      if (usesStandardSymbols) {
        return template;
      } else {
        if (!template || !angular.isString(template)) return template;
        return template.replace(/\{\{/g, startSymbol).replace(/}}/g, endSymbol);
      }
    },

    /**
     * Scan up dom hierarchy for enabled parent;
     */
    getParentWithPointerEvents: function (element) {
      var parent = element.parent();

      // jqLite might return a non-null, but still empty, parent; so check for parent and length
      while (hasComputedStyle(parent, 'pointer-events', 'none')) {
        parent = parent.parent();
      }

      return parent;
    },

    getNearestContentElement: function (element) {
      var current = element.parent()[0];
      // Look for the nearest parent md-content, stopping at the rootElement.
      while (current && current !== $rootElement[0] && current !== document.body && current.nodeName.toUpperCase() !== 'MD-CONTENT') {
        current = current.parentNode;
      }
      return current;
    },

    /**
     * Checks if the current browser is natively supporting the `sticky` position.
     * @returns {string} supported sticky property name
     */
    checkStickySupport: function() {
      var stickyProp;
      var testEl = angular.element('<div>');
      $document[0].body.appendChild(testEl[0]);

      var stickyProps = ['sticky', '-webkit-sticky'];
      for (var i = 0; i < stickyProps.length; ++i) {
        testEl.css({
          position: stickyProps[i],
          top: 0,
          'z-index': 2
        });

        if (testEl.css('position') == stickyProps[i]) {
          stickyProp = stickyProps[i];
          break;
        }
      }

      testEl.remove();

      return stickyProp;
    },

    /**
     * Parses an attribute value, mostly a string.
     * By default checks for negated values and returns `false´ if present.
     * Negated values are: (native falsy) and negative strings like:
     * `false` or `0`.
     * @param value Attribute value which should be parsed.
     * @param negatedCheck When set to false, won't check for negated values.
     * @returns {boolean}
     */
    parseAttributeBoolean: function(value, negatedCheck) {
      return value === '' || !!value && (negatedCheck === false || value !== 'false' && value !== '0');
    },

    hasComputedStyle: hasComputedStyle,

    /**
     * Returns true if the parent form of the element has been submitted.
     *
     * @param element An AngularJS or HTML5 element.
     *
     * @returns {boolean}
     */
    isParentFormSubmitted: function(element) {
      var parent = $mdUtil.getClosest(element, 'form');
      var form = parent ? angular.element(parent).controller('form') : null;

      return form ? form.$submitted : false;
    },

    /**
     * Animate the requested element's scrollTop to the requested scrollPosition with basic easing.
     *
     * @param {!HTMLElement} element The element to scroll.
     * @param {number} scrollEnd The new/final scroll position.
     * @param {number=} duration Duration of the scroll. Default is 1000ms.
     */
    animateScrollTo: function(element, scrollEnd, duration) {
      var scrollStart = element.scrollTop;
      var scrollChange = scrollEnd - scrollStart;
      var scrollingDown = scrollStart < scrollEnd;
      var startTime = $mdUtil.now();

      $$rAF(scrollChunk);

      function scrollChunk() {
        var newPosition = calculateNewPosition();

        element.scrollTop = newPosition;

        if (scrollingDown ? newPosition < scrollEnd : newPosition > scrollEnd) {
          $$rAF(scrollChunk);
        }
      }

      function calculateNewPosition() {
        var easeDuration = duration || 1000;
        var currentTime = $mdUtil.now() - startTime;

        return ease(currentTime, scrollStart, scrollChange, easeDuration);
      }

      function ease(currentTime, start, change, duration) {
        // If the duration has passed (which can occur if our app loses focus due to $$rAF), jump
        // straight to the proper position
        if (currentTime > duration) {
          return start + change;
        }

        var ts = (currentTime /= duration) * currentTime;
        var tc = ts * currentTime;

        return start + change * (-2 * tc + 3 * ts);
      }
    },

    /**
     * Provides an easy mechanism for removing duplicates from an array.
     *
     *    var myArray = [1, 2, 2, 3, 3, 3, 4, 4, 4, 4];
     *
     *    $mdUtil.uniq(myArray) => [1, 2, 3, 4]
     *
     * @param {array} array The array whose unique values should be returned.
     *
     * @returns {array} A copy of the array containing only unique values.
     */
    uniq: function(array) {
      if (!array) { return; }

      return array.filter(function(value, index, self) {
        return self.indexOf(value) === index;
      });
    }
  };


// Instantiate other namespace utility methods

  $mdUtil.dom.animator = $$mdAnimate($mdUtil);

  return $mdUtil;

  function getNode(el) {
    return el[0] || el;
  }

}

/*
 * Since removing jQuery from the demos, some code that uses `element.focus()` is broken.
 * We need to add `element.focus()`, because it's testable unlike `element[0].focus`.
 */

angular.element.prototype.focus = angular.element.prototype.focus || function() {
    if (this.length) {
      this[0].focus();
    }
    return this;
  };
angular.element.prototype.blur = angular.element.prototype.blur || function() {
    if (this.length) {
      this[0].blur();
    }
    return this;
  };

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.aria
 * @description
 * Aria Expectations for AngularJS Material components.
 */
MdAriaService.$inject = ["$$rAF", "$log", "$window", "$interpolate"];
angular
  .module('material.core')
  .provider('$mdAria', MdAriaProvider);

/**
 * @ngdoc service
 * @name $mdAriaProvider
 * @module material.core.aria
 *
 * @description
 *
 * Modify options of the `$mdAria` service, which will be used by most of the AngularJS Material
 * components.
 *
 * You are able to disable `$mdAria` warnings, by using the following markup.
 *
 * <hljs lang="js">
 *   app.config(function($mdAriaProvider) {
 *     // Globally disables all ARIA warnings.
 *     $mdAriaProvider.disableWarnings();
 *   });
 * </hljs>
 *
 */
function MdAriaProvider() {

  var config = {
    /** Whether we should show ARIA warnings in the console if labels are missing on the element */
    showWarnings: true
  };

  return {
    disableWarnings: disableWarnings,
    $get: ["$$rAF", "$log", "$window", "$interpolate", function($$rAF, $log, $window, $interpolate) {
      return MdAriaService.apply(config, arguments);
    }]
  };

  /**
   * @ngdoc method
   * @name $mdAriaProvider#disableWarnings
   * @description Disables all ARIA warnings generated by AngularJS Material.
   */
  function disableWarnings() {
    config.showWarnings = false;
  }
}

/*
 * @ngInject
 */
function MdAriaService($$rAF, $log, $window, $interpolate) {

  // Load the showWarnings option from the current context and store it inside of a scope variable,
  // because the context will be probably lost in some function calls.
  var showWarnings = this.showWarnings;

  return {
    expect: expect,
    expectAsync: expectAsync,
    expectWithText: expectWithText,
    expectWithoutText: expectWithoutText,
    getText: getText,
    hasAriaLabel: hasAriaLabel,
    parentHasAriaLabel: parentHasAriaLabel
  };

  /**
   * Check if expected attribute has been specified on the target element or child
   * @param element
   * @param attrName
   * @param {optional} defaultValue What to set the attr to if no value is found
   */
  function expect(element, attrName, defaultValue) {

    var node = angular.element(element)[0] || element;

    // if node exists and neither it nor its children have the attribute
    if (node &&
       ((!node.hasAttribute(attrName) ||
        node.getAttribute(attrName).length === 0) &&
        !childHasAttribute(node, attrName))) {

      defaultValue = angular.isString(defaultValue) ? defaultValue.trim() : '';
      if (defaultValue.length) {
        element.attr(attrName, defaultValue);
      } else if (showWarnings) {
        $log.warn('ARIA: Attribute "', attrName, '", required for accessibility, is missing on node:', node);
      }

    }
  }

  function expectAsync(element, attrName, defaultValueGetter) {
    // Problem: when retrieving the element's contents synchronously to find the label,
    // the text may not be defined yet in the case of a binding.
    // There is a higher chance that a binding will be defined if we wait one frame.
    $$rAF(function() {
        expect(element, attrName, defaultValueGetter());
    });
  }

  function expectWithText(element, attrName) {
    var content = getText(element) || "";
    var hasBinding = content.indexOf($interpolate.startSymbol()) > -1;

    if (hasBinding) {
      expectAsync(element, attrName, function() {
        return getText(element);
      });
    } else {
      expect(element, attrName, content);
    }
  }

  function expectWithoutText(element, attrName) {
    var content = getText(element);
    var hasBinding = content.indexOf($interpolate.startSymbol()) > -1;

    if ( !hasBinding && !content) {
      expect(element, attrName, content);
    }
  }

  function getText(element) {
    element = element[0] || element;
    var walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    var text = '';

    var node;
    while (node = walker.nextNode()) {
      if (!isAriaHiddenNode(node)) {
        text += node.textContent;
      }
    }

    return text.trim() || '';

    function isAriaHiddenNode(node) {
      while (node.parentNode && (node = node.parentNode) !== element) {
        if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') {
          return true;
        }
      }
    }
  }

  function childHasAttribute(node, attrName) {
    var hasChildren = node.hasChildNodes(),
        hasAttr = false;

    function isHidden(el) {
      var style = el.currentStyle ? el.currentStyle : $window.getComputedStyle(el);
      return (style.display === 'none');
    }

    if (hasChildren) {
      var children = node.childNodes;
      for (var i=0; i < children.length; i++) {
        var child = children[i];
        if (child.nodeType === 1 && child.hasAttribute(attrName)) {
          if (!isHidden(child)) {
            hasAttr = true;
          }
        }
      }
    }
    return hasAttr;
  }

  /**
   * Check if expected element has aria label attribute
   * @param element
   */
  function hasAriaLabel(element) {
    var node = angular.element(element)[0] || element;

    /* Check if compatible node type (ie: not HTML Document node) */
    if (!node.hasAttribute) {
      return false;
    }

    /* Check label or description attributes */
    return node.hasAttribute('aria-label') || node.hasAttribute('aria-labelledby') || node.hasAttribute('aria-describedby');
  }

  /**
   * Check if expected element's parent has aria label attribute and has valid role and tagName
   * @param element
   * @param {optional} level Number of levels deep search should be performed
   */
  function parentHasAriaLabel(element, level) {
    level = level || 1;
    var node = angular.element(element)[0] || element;
    if (!node.parentNode) {
      return false;
    }
    if (performCheck(node.parentNode)) {
      return true;
    }
    level--;
    if (level) {
      return parentHasAriaLabel(node.parentNode, level);
    }
    return false;

    function performCheck(parentNode) {
      if (!hasAriaLabel(parentNode)) {
        return false;
      }
      /* Perform role blacklist check */
      if (parentNode.hasAttribute('role')) {
        switch(parentNode.getAttribute('role').toLowerCase()) {
          case 'command':
          case 'definition':
          case 'directory':
          case 'grid':
          case 'list':
          case 'listitem':
          case 'log':
          case 'marquee':
          case 'menu':
          case 'menubar':
          case 'note':
          case 'presentation':
          case 'separator':
          case 'scrollbar':
          case 'status':
          case 'tablist':
            return false;
        }
      }
      /* Perform tagName blacklist check */
      switch(parentNode.tagName.toLowerCase()) {
        case 'abbr':
        case 'acronym':
        case 'address':
        case 'applet':
        case 'audio':
        case 'b':
        case 'bdi':
        case 'bdo':
        case 'big':
        case 'blockquote':
        case 'br':
        case 'canvas':
        case 'caption':
        case 'center':
        case 'cite':
        case 'code':
        case 'col':
        case 'data':
        case 'dd':
        case 'del':
        case 'dfn':
        case 'dir':
        case 'div':
        case 'dl':
        case 'em':
        case 'embed':
        case 'fieldset':
        case 'figcaption':
        case 'font':
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
        case 'hgroup':
        case 'html':
        case 'i':
        case 'ins':
        case 'isindex':
        case 'kbd':
        case 'keygen':
        case 'label':
        case 'legend':
        case 'li':
        case 'map':
        case 'mark':
        case 'menu':
        case 'object':
        case 'ol':
        case 'output':
        case 'pre':
        case 'presentation':
        case 'q':
        case 'rt':
        case 'ruby':
        case 'samp':
        case 'small':
        case 'source':
        case 'span':
        case 'status':
        case 'strike':
        case 'strong':
        case 'sub':
        case 'sup':
        case 'svg':
        case 'tbody':
        case 'td':
        case 'th':
        case 'thead':
        case 'time':
        case 'tr':
        case 'track':
        case 'tt':
        case 'ul':
        case 'var':
          return false;
      }
      return true;
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.compiler
 * @description
 * AngularJS Material template and element compiler.
 */
angular
  .module('material.core')
  .provider('$mdCompiler', MdCompilerProvider);

/**
 * @ngdoc provider
 * @name $mdCompilerProvider
 *
 * @description
 */
/**
 * @ngdoc service
 * @name $mdCompiler
 * @module material.core.compiler
 * @description
 * The $mdCompiler service is an abstraction of AngularJS's compiler, that allows developers
 * to easily compile an element with options like in a Directive Definition Object.
 *
 * > The compiler powers a lot of components inside of AngularJS Material.
 * > Like the `$mdPanel` or `$mdDialog`.
 *
 * @usage
 *
 * Basic Usage with a template
 *
 * <hljs lang="js">
 *   $mdCompiler.compile({
 *     templateUrl: 'modal.html',
 *     controller: 'ModalCtrl',
 *     locals: {
 *       modal: myModalInstance;
 *     }
 *   }).then(function (compileData) {
 *     compileData.element; // Compiled DOM element
 *     compileData.link(myScope); // Instantiate controller and link element to scope.
 *   });
 * </hljs>
 *
 * Example with a content element
 *
 * <hljs lang="js">
 *
 *   // Create a virtual element and link it manually.
 *   // The compiler doesn't need to recompile the element each time.
 *   var myElement = $compile('<span>Test</span>')(myScope);
 *
 *   $mdCompiler.compile({
 *     contentElement: myElement
 *   }).then(function (compileData) {
 *     compileData.element // Content Element (same as above)
 *     compileData.link // This does nothing when using a contentElement.
 *   });
 * </hljs>
 *
 * > Content Element is a significant performance improvement when the developer already knows that the
 * > compiled element will be always the same and the scope will not change either.
 *
 * The `contentElement` option also supports DOM elements which will be temporary removed and restored
 * at its old position.
 *
 * <hljs lang="js">
 *   var domElement = document.querySelector('#myElement');
 *
 *   $mdCompiler.compile({
 *     contentElement: myElement
 *   }).then(function (compileData) {
 *     compileData.element // Content Element (same as above)
 *     compileData.link // This does nothing when using a contentElement.
 *   });
 * </hljs>
 *
 * The `$mdCompiler` can also query for the element in the DOM itself.
 *
 * <hljs lang="js">
 *   $mdCompiler.compile({
 *     contentElement: '#myElement'
 *   }).then(function (compileData) {
 *     compileData.element // Content Element (same as above)
 *     compileData.link // This does nothing when using a contentElement.
 *   });
 * </hljs>
 *
 */
MdCompilerProvider.$inject = ['$compileProvider'];
function MdCompilerProvider($compileProvider) {

  var provider = this;

  /**
   * @name  $mdCompilerProvider#respectPreAssignBindingsEnabled
   *
   * @param {boolean=} respected update the respectPreAssignBindingsEnabled state if provided, otherwise just return
   * the current Material preAssignBindingsEnabled state
   * @returns {*} current value if used as getter or itself (chaining) if used as setter
   *
   * @description
   * Call this method to enable/disable whether Material-specific (dialogs/toasts) controllers respect the AngularJS
   * `$compile.preAssignBindingsEnabled` flag. Note that this doesn't affect directives/components created via
   * regular AngularJS methods which constitute most Material & user-created components.
   *
   * @see [AngularJS documentation for `$compile.preAssignBindingsEnabled`
   * ](https://code.angularjs.org/1.6.4/docs/api/ng/provider/$compileProvider#preAssignBindingsEnabled)
   * for more information.
   *
   * If disabled (false), the compiler assigns the value of each of the bindings to the
   * properties of the controller object before the constructor of this object is called.
   *
   * If enabled (true) the behavior depends on the AngularJS version used:
   *
   *     - `<1.5.10` - bindings are pre-assigned
   *     - `>=1.5.10 <1.7` - behaves like set to whatever `$compileProvider.preAssignBindingsEnabled()` reports; if
   *                         the `$compileProvider` flag wasn't set manually, it defaults to pre-assigning bindings
   *                         with AngularJS `1.5.x` & to calling the constructor first with AngularJS `1.6.x`.
   *     - `>=1.7` - the compiler calls the constructor first before assigning bindings
   *
   * The default value is `false` but will change to `true` in AngularJS Material 1.2.
   *
   * It is recommended to set this flag to `true` in AngularJS Material 1.1.x; the only reason it's not set that way
   * by default is backwards compatibility. Not setting the flag to `true` when
   * `$compileProvider.preAssignBindingsEnabled()` is set to `false` (i.e. default behavior in AngularJS 1.6.0 or newer)
   * makes it hard to unit test Material Dialog/Toast controllers using the `$controller` helper as it always follows
   * the `$compileProvider.preAssignBindingsEnabled()` value.
   */
  // TODO change it to `true` in Material 1.2.
  var respectPreAssignBindingsEnabled = false;
  this.respectPreAssignBindingsEnabled = function(respected) {
    if (angular.isDefined(respected)) {
      respectPreAssignBindingsEnabled = respected;
      return this;
    }

    return respectPreAssignBindingsEnabled;
  };

  /**
   * @description
   * This function returns `true` if Material-specific (dialogs/toasts) controllers have bindings pre-assigned in
   * controller constructors and `false` otherwise.
   *
   * Note that this doesn't affect directives/components created via regular AngularJS methods which constitute most
   * Material & user-created components; their behavior can be checked via `$compileProvider.preAssignBindingsEnabled()`
   * in AngularJS `>=1.5.10 <1.7.0`.
   *
   * @returns {*} current preAssignBindingsEnabled state
   */
  function getPreAssignBindingsEnabled() {
    if (!respectPreAssignBindingsEnabled) {
      // respectPreAssignBindingsEnabled === false
      // We're ignoring the AngularJS `$compileProvider.preAssignBindingsEnabled()` value in this case.
      return true;
    }

    // respectPreAssignBindingsEnabled === true

    if (typeof $compileProvider.preAssignBindingsEnabled === 'function') {
      return $compileProvider.preAssignBindingsEnabled();
    }

    // Flag respected but not present => apply logic based on AngularJS version used.
    if (angular.version.major === 1 && angular.version.minor < 6) {
      // AngularJS <1.5.10
      return true;
    }

    // AngularJS >=1.7.0
    return false;
  }

  this.$get = ["$q", "$templateRequest", "$injector", "$compile", "$controller",
    function($q, $templateRequest, $injector, $compile, $controller) {
      return new MdCompilerService($q, $templateRequest, $injector, $compile, $controller);
    }];

  function MdCompilerService($q, $templateRequest, $injector, $compile, $controller) {

    /** @private @const {!angular.$q} */
    this.$q = $q;

    /** @private @const {!angular.$templateRequest} */
    this.$templateRequest = $templateRequest;

    /** @private @const {!angular.$injector} */
    this.$injector = $injector;

    /** @private @const {!angular.$compile} */
    this.$compile = $compile;

    /** @private @const {!angular.$controller} */
    this.$controller = $controller;
  }

  /**
   * @ngdoc method
   * @name $mdCompiler#compile
   * @description
   *
   * A method to compile a HTML template with the AngularJS compiler.
   * The `$mdCompiler` is wrapper around the AngularJS compiler and provides extra functionality
   * like controller instantiation or async resolves.
   *
   * @param {!Object} options An options object, with the following properties:
   *
   *    - `controller` - `{string|Function}` Controller fn that should be associated with
   *         newly created scope or the name of a registered controller if passed as a string.
   *    - `controllerAs` - `{string=}` A controller alias name. If present the controller will be
   *         published to scope under the `controllerAs` name.
   *    - `contentElement` - `{string|Element}`: Instead of using a template, which will be
   *         compiled each time, you can also use a DOM element.<br/>
   *    - `template` - `{string=}` An html template as a string.
   *    - `templateUrl` - `{string=}` A path to an html template.
   *    - `transformTemplate` - `{function(template)=}` A function which transforms the template after
   *        it is loaded. It will be given the template string as a parameter, and should
   *        return a a new string representing the transformed template.
   *    - `resolve` - `{Object.<string, function>=}` - An optional map of dependencies which should
   *        be injected into the controller. If any of these dependencies are promises, the compiler
   *        will wait for them all to be resolved, or if one is rejected before the controller is
   *        instantiated `compile()` will fail..
   *      * `key` - `{string}`: a name of a dependency to be injected into the controller.
   *      * `factory` - `{string|function}`: If `string` then it is an alias for a service.
   *        Otherwise if function, then it is injected and the return value is treated as the
   *        dependency. If the result is a promise, it is resolved before its value is
   *        injected into the controller.
   *
   * @returns {Object} promise A promise, which will be resolved with a `compileData` object.
   * `compileData` has the following properties:
   *
   *   - `element` - `{element}`: an uncompiled element matching the provided template.
   *   - `link` - `{function(scope)}`: A link function, which, when called, will compile
   *     the element and instantiate the provided controller (if given).
   *   - `locals` - `{object}`: The locals which will be passed into the controller once `link` is
   *     called. If `bindToController` is true, they will be coppied to the ctrl instead
   *
   */
  MdCompilerService.prototype.compile = function(options) {

    if (options.contentElement) {
      return this._prepareContentElement(options);
    } else {
      return this._compileTemplate(options);
    }

  };

  /**
   * Instead of compiling any template, the compiler just fetches an existing HTML element from the DOM and
   * provides a restore function to put the element back it old DOM position.
   * @param {!Object} options Options to be used for the compiler.
   */
  MdCompilerService.prototype._prepareContentElement = function(options) {

    var contentElement = this._fetchContentElement(options);

    return this.$q.resolve({
      element: contentElement.element,
      cleanup: contentElement.restore,
      locals: {},
      link: function() {
        return contentElement.element;
      }
    });

  };

  /**
   * Compiles a template by considering all options and waiting for all resolves to be ready.
   * @param {!Object} options Compile options
   * @returns {!Object} Compile data with link function.
   */
  MdCompilerService.prototype._compileTemplate = function(options) {

    var self = this;
    var templateUrl = options.templateUrl;
    var template = options.template || '';
    var resolve = angular.extend({}, options.resolve);
    var locals = angular.extend({}, options.locals);
    var transformTemplate = options.transformTemplate || angular.identity;

    // Take resolve values and invoke them.
    // Resolves can either be a string (value: 'MyRegisteredAngularConst'),
    // or an invokable 'factory' of sorts: (value: function ValueGetter($dependency) {})
    angular.forEach(resolve, function(value, key) {
      if (angular.isString(value)) {
        resolve[key] = self.$injector.get(value);
      } else {
        resolve[key] = self.$injector.invoke(value);
      }
    });

    // Add the locals, which are just straight values to inject
    // eg locals: { three: 3 }, will inject three into the controller
    angular.extend(resolve, locals);

    if (templateUrl) {
      resolve.$$ngTemplate = this.$templateRequest(templateUrl);
    } else {
      resolve.$$ngTemplate = this.$q.when(template);
    }


    // Wait for all the resolves to finish if they are promises
    return this.$q.all(resolve).then(function(locals) {

      var template = transformTemplate(locals.$$ngTemplate, options);
      var element = options.element || angular.element('<div>').html(template.trim()).contents();

      return self._compileElement(locals, element, options);
    });

  };

  /**
   * Method to compile an element with the given options.
   * @param {!Object} locals Locals to be injected to the controller if present
   * @param {!JQLite} element Element to be compiled and linked
   * @param {!Object} options Options to be used for linking.
   * @returns {!Object} Compile data with link function.
   */
  MdCompilerService.prototype._compileElement = function(locals, element, options) {
    var self = this;
    var ngLinkFn = this.$compile(element);

    var compileData = {
      element: element,
      cleanup: element.remove.bind(element),
      locals: locals,
      link: linkFn
    };

    function linkFn(scope) {
      locals.$scope = scope;

      // Instantiate controller if the developer provided one.
      if (options.controller) {

        var injectLocals = angular.extend({}, locals, {
          $element: element
        });

        // Create the specified controller instance.
        var ctrl = self._createController(options, injectLocals, locals);

        // Unique identifier for AngularJS Route ngView controllers.
        element.data('$ngControllerController', ctrl);
        element.children().data('$ngControllerController', ctrl);

        // Expose the instantiated controller to the compile data
        compileData.controller = ctrl;
      }

      // Invoke the AngularJS $compile link function.
      return ngLinkFn(scope);
    }

    return compileData;

  };

  /**
   * Creates and instantiates a new controller with the specified options.
   * @param {!Object} options Options that include the controller
   * @param {!Object} injectLocals Locals to to be provided in the controller DI.
   * @param {!Object} locals Locals to be injected to the controller.
   * @returns {!Object} Created controller instance.
   */
  MdCompilerService.prototype._createController = function(options, injectLocals, locals) {
    var invokeCtrl = this.$controller(options.controller, injectLocals, true, options.controllerAs);

    if (getPreAssignBindingsEnabled() && options.bindToController) {
      angular.extend(invokeCtrl.instance, locals);
    }

    // Instantiate and initialize the specified controller.
    var ctrl = invokeCtrl();

    if (!getPreAssignBindingsEnabled() && options.bindToController) {
      angular.extend(invokeCtrl.instance, locals);
    }

    // Call the $onInit hook if it's present on the controller.
    angular.isFunction(ctrl.$onInit) && ctrl.$onInit();

    return ctrl;
  };

  /**
   * Fetches an element removing it from the DOM and using it temporary for the compiler.
   * Elements which were fetched will be restored after use.
   * @param {!Object} options Options to be used for the compilation.
   * @returns {{element: !JQLite, restore: !Function}}
   */
  MdCompilerService.prototype._fetchContentElement = function(options) {

    var contentEl = options.contentElement;
    var restoreFn = null;

    if (angular.isString(contentEl)) {
      contentEl = document.querySelector(contentEl);
      restoreFn = createRestoreFn(contentEl);
    } else {
      contentEl = contentEl[0] || contentEl;

      // When the element is visible in the DOM, then we restore it at close of the dialog.
      // Otherwise it will be removed from the DOM after close.
      if (document.contains(contentEl)) {
        restoreFn = createRestoreFn(contentEl);
      } else {
        restoreFn = function() {
          if (contentEl.parentNode) {
            contentEl.parentNode.removeChild(contentEl);
          }
        }
      }
    }

    return {
      element: angular.element(contentEl),
      restore: restoreFn
    };

    function createRestoreFn(element) {
      var parent = element.parentNode;
      var nextSibling = element.nextElementSibling;

      return function() {
        if (!nextSibling) {
          // When the element didn't had any sibling, then it can be simply appended to the
          // parent, because it plays no role, which index it had before.
          parent.appendChild(element);
        } else {
          // When the element had a sibling, which marks the previous position of the element
          // in the DOM, we insert it correctly before the sibling, to have the same index as
          // before.
          parent.insertBefore(element, nextSibling);
        }
      }
    }
  };
}


})();
(function(){
"use strict";


MdGesture.$inject = ["$$MdGestureHandler", "$$rAF", "$timeout"];
attachToDocument.$inject = ["$mdGesture", "$$MdGestureHandler"];var HANDLERS = {};

/* The state of the current 'pointer'
 * The pointer represents the state of the current touch.
 * It contains normalized x and y coordinates from DOM events,
 * as well as other information abstracted from the DOM.
 */

var pointer, lastPointer, forceSkipClickHijack = false, maxClickDistance = 6;

/**
 * The position of the most recent click if that click was on a label element.
 * @type {{x: number, y: number}?}
 */
var lastLabelClickPos = null;

// Used to attach event listeners once when multiple ng-apps are running.
var isInitialized = false;

angular
  .module('material.core.gestures', [ ])
  .provider('$mdGesture', MdGestureProvider)
  .factory('$$MdGestureHandler', MdGestureHandler)
  .run(attachToDocument );

/**
   * @ngdoc service
   * @name $mdGestureProvider
   * @module material.core.gestures
   *
   * @description
   * In some scenarios on Mobile devices (without jQuery), the click events should NOT be hijacked.
   * `$mdGestureProvider` is used to configure the Gesture module to ignore or skip click hijacking on mobile
   * devices.
   * You can also change max click distance (6px by default) if you have issues on some touch screens.
   *
   * <hljs lang="js">
   *   app.config(function($mdGestureProvider) {
   *
   *     // For mobile devices without jQuery loaded, do not
   *     // intercept click events during the capture phase.
   *     $mdGestureProvider.skipClickHijack();
   *
   *     // If hijcacking clicks, change default 6px click distance
   *     $mdGestureProvider.setMaxClickDistance(12);
   *
   *   });
   * </hljs>
   *
   */
function MdGestureProvider() { }

MdGestureProvider.prototype = {

  // Publish access to setter to configure a variable  BEFORE the
  // $mdGesture service is instantiated...
  skipClickHijack: function() {
    return forceSkipClickHijack = true;
  },

  setMaxClickDistance: function(clickDistance) {
    maxClickDistance = parseInt(clickDistance);
  },

  /**
   * $get is used to build an instance of $mdGesture
   * @ngInject
   */
  $get : ["$$MdGestureHandler", "$$rAF", "$timeout", function($$MdGestureHandler, $$rAF, $timeout) {
       return new MdGesture($$MdGestureHandler, $$rAF, $timeout);
  }]
};



/**
 * MdGesture factory construction function
 * @ngInject
 */
function MdGesture($$MdGestureHandler, $$rAF, $timeout) {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera;
  var isIos = userAgent.match(/ipad|iphone|ipod/i);
  var isAndroid = userAgent.match(/android/i);
  var touchActionProperty = getTouchAction();
  var hasJQuery =  (typeof window.jQuery !== 'undefined') && (angular.element === window.jQuery);

  var self = {
    handler: addHandler,
    register: register,
    // On mobile w/out jQuery, we normally intercept clicks. Should we skip that?
    isHijackingClicks: (isIos || isAndroid) && !hasJQuery && !forceSkipClickHijack
  };

  if (self.isHijackingClicks) {
    self.handler('click', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: checkDistanceAndEmit('click')
    });

    self.handler('focus', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: function(ev, pointer) {
        if (pointer.distance < this.state.options.maxDistance && canFocus(ev.target)) {
          this.dispatchEvent(ev, 'focus', pointer);
          ev.target.focus();
        }
      }
    });

    self.handler('mouseup', {
      options: {
        maxDistance: maxClickDistance
      },
      onEnd: checkDistanceAndEmit('mouseup')
    });

    self.handler('mousedown', {
      onStart: function(ev) {
        this.dispatchEvent(ev, 'mousedown');
      }
    });
  }

  function checkDistanceAndEmit(eventName) {
    return function(ev, pointer) {
      if (pointer.distance < this.state.options.maxDistance) {
        this.dispatchEvent(ev, eventName, pointer);
      }
    };
  }

  /*
   * Register an element to listen for a handler.
   * This allows an element to override the default options for a handler.
   * Additionally, some handlers like drag and hold only dispatch events if
   * the domEvent happens inside an element that's registered to listen for these events.
   *
   * @see GestureHandler for how overriding of default options works.
   * @example $mdGesture.register(myElement, 'drag', { minDistance: 20, horziontal: false })
   */
  function register(element, handlerName, options) {
    var handler = HANDLERS[handlerName.replace(/^\$md./, '')];
    if (!handler) {
      throw new Error('Failed to register element with handler ' + handlerName + '. ' +
      'Available handlers: ' + Object.keys(HANDLERS).join(', '));
    }
    return handler.registerElement(element, options);
  }

  /*
   * add a handler to $mdGesture. see below.
   */
  function addHandler(name, definition) {
    var handler = new $$MdGestureHandler(name);
    angular.extend(handler, definition);
    HANDLERS[name] = handler;

    return self;
  }

  /*
   * Register handlers. These listen to touch/start/move events, interpret them,
   * and dispatch gesture events depending on options & conditions. These are all
   * instances of GestureHandler.
   * @see GestureHandler
   */
  return self
    /*
     * The press handler dispatches an event on touchdown/touchend.
     * It's a simple abstraction of touch/mouse/pointer start and end.
     */
    .handler('press', {
      onStart: function (ev, pointer) {
        this.dispatchEvent(ev, '$md.pressdown');
      },
      onEnd: function (ev, pointer) {
        this.dispatchEvent(ev, '$md.pressup');
      }
    })

    /*
     * The hold handler dispatches an event if the user keeps their finger within
     * the same <maxDistance> area for <delay> ms.
     * The hold handler will only run if a parent of the touch target is registered
     * to listen for hold events through $mdGesture.register()
     */
    .handler('hold', {
      options: {
        maxDistance: 6,
        delay: 500
      },
      onCancel: function () {
        $timeout.cancel(this.state.timeout);
      },
      onStart: function (ev, pointer) {
        // For hold, require a parent to be registered with $mdGesture.register()
        // Because we prevent scroll events, this is necessary.
        if (!this.state.registeredParent) return this.cancel();

        this.state.pos = {x: pointer.x, y: pointer.y};
        this.state.timeout = $timeout(angular.bind(this, function holdDelayFn() {
          this.dispatchEvent(ev, '$md.hold');
          this.cancel(); //we're done!
        }), this.state.options.delay, false);
      },
      onMove: function (ev, pointer) {
        // Don't scroll while waiting for hold.
        // If we don't preventDefault touchmove events here, Android will assume we don't
        // want to listen to anymore touch events. It will start scrolling and stop sending
        // touchmove events.
        if (!touchActionProperty && ev.type === 'touchmove') ev.preventDefault();

        // If the user moves greater than <maxDistance> pixels, stop the hold timer
        // set in onStart
        var dx = this.state.pos.x - pointer.x;
        var dy = this.state.pos.y - pointer.y;
        if (Math.sqrt(dx * dx + dy * dy) > this.options.maxDistance) {
          this.cancel();
        }
      },
      onEnd: function () {
        this.onCancel();
      }
    })

    /*
     * The drag handler dispatches a drag event if the user holds and moves his finger greater than
     * <minDistance> px in the x or y direction, depending on options.horizontal.
     * The drag will be cancelled if the user moves his finger greater than <minDistance>*<cancelMultiplier> in
     * the perpendicular direction. Eg if the drag is horizontal and the user moves his finger <minDistance>*<cancelMultiplier>
     * pixels vertically, this handler won't consider the move part of a drag.
     */
    .handler('drag', {
      options: {
        minDistance: 6,
        horizontal: true,
        cancelMultiplier: 1.5
      },
      onSetup: function(element, options) {
        if (touchActionProperty) {
          // We check for horizontal to be false, because otherwise we would overwrite the default opts.
          this.oldTouchAction = element[0].style[touchActionProperty];
          element[0].style[touchActionProperty] = options.horizontal ? 'pan-y' : 'pan-x';
        }
      },
      onCleanup: function(element) {
        if (this.oldTouchAction) {
          element[0].style[touchActionProperty] = this.oldTouchAction;
        }
      },
      onStart: function (ev) {
        // For drag, require a parent to be registered with $mdGesture.register()
        if (!this.state.registeredParent) this.cancel();
      },
      onMove: function (ev, pointer) {
        var shouldStartDrag, shouldCancel;
        // Don't scroll while deciding if this touchmove qualifies as a drag event.
        // If we don't preventDefault touchmove events here, Android will assume we don't
        // want to listen to anymore touch events. It will start scrolling and stop sending
        // touchmove events.
        if (!touchActionProperty && ev.type === 'touchmove') ev.preventDefault();

        if (!this.state.dragPointer) {
          if (this.state.options.horizontal) {
            shouldStartDrag = Math.abs(pointer.distanceX) > this.state.options.minDistance;
            shouldCancel = Math.abs(pointer.distanceY) > this.state.options.minDistance * this.state.options.cancelMultiplier;
          } else {
            shouldStartDrag = Math.abs(pointer.distanceY) > this.state.options.minDistance;
            shouldCancel = Math.abs(pointer.distanceX) > this.state.options.minDistance * this.state.options.cancelMultiplier;
          }

          if (shouldStartDrag) {
            // Create a new pointer representing this drag, starting at this point where the drag started.
            this.state.dragPointer = makeStartPointer(ev);
            updatePointerState(ev, this.state.dragPointer);
            this.dispatchEvent(ev, '$md.dragstart', this.state.dragPointer);

          } else if (shouldCancel) {
            this.cancel();
          }
        } else {
          this.dispatchDragMove(ev);
        }
      },
      // Only dispatch dragmove events every frame; any more is unnecessary
      dispatchDragMove: $$rAF.throttle(function (ev) {
        // Make sure the drag didn't stop while waiting for the next frame
        if (this.state.isRunning) {
          updatePointerState(ev, this.state.dragPointer);
          this.dispatchEvent(ev, '$md.drag', this.state.dragPointer);
        }
      }),
      onEnd: function (ev, pointer) {
        if (this.state.dragPointer) {
          updatePointerState(ev, this.state.dragPointer);
          this.dispatchEvent(ev, '$md.dragend', this.state.dragPointer);
        }
      }
    })

    /*
     * The swipe handler will dispatch a swipe event if, on the end of a touch,
     * the velocity and distance were high enough.
     */
    .handler('swipe', {
      options: {
        minVelocity: 0.65,
        minDistance: 10
      },
      onEnd: function (ev, pointer) {
        var eventType;

        if (Math.abs(pointer.velocityX) > this.state.options.minVelocity &&
          Math.abs(pointer.distanceX) > this.state.options.minDistance) {
          eventType = pointer.directionX == 'left' ? '$md.swipeleft' : '$md.swiperight';
          this.dispatchEvent(ev, eventType);
        }
        else if (Math.abs(pointer.velocityY) > this.state.options.minVelocity &&
          Math.abs(pointer.distanceY) > this.state.options.minDistance) {
          eventType = pointer.directionY == 'up' ? '$md.swipeup' : '$md.swipedown';
          this.dispatchEvent(ev, eventType);
        }
      }
    });

  function getTouchAction() {
    var testEl = document.createElement('div');
    var vendorPrefixes = ['', 'webkit', 'Moz', 'MS', 'ms', 'o'];

    for (var i = 0; i < vendorPrefixes.length; i++) {
      var prefix = vendorPrefixes[i];
      var property = prefix ? prefix + 'TouchAction' : 'touchAction';
      if (angular.isDefined(testEl.style[property])) {
        return property;
      }
    }
  }

}

/**
 * MdGestureHandler
 * A GestureHandler is an object which is able to dispatch custom dom events
 * based on native dom {touch,pointer,mouse}{start,move,end} events.
 *
 * A gesture will manage its lifecycle through the start,move,end, and cancel
 * functions, which are called by native dom events.
 *
 * A gesture has the concept of 'options' (eg a swipe's required velocity), which can be
 * overridden by elements registering through $mdGesture.register()
 */
function GestureHandler (name) {
  this.name = name;
  this.state = {};
}

function MdGestureHandler() {
  var hasJQuery =  (typeof window.jQuery !== 'undefined') && (angular.element === window.jQuery);

  GestureHandler.prototype = {
    options: {},
    // jQuery listeners don't work with custom DOMEvents, so we have to dispatch events
    // differently when jQuery is loaded
    dispatchEvent: hasJQuery ?  jQueryDispatchEvent : nativeDispatchEvent,

    // These are overridden by the registered handler
    onSetup: angular.noop,
    onCleanup: angular.noop,
    onStart: angular.noop,
    onMove: angular.noop,
    onEnd: angular.noop,
    onCancel: angular.noop,

    // onStart sets up a new state for the handler, which includes options from the
    // nearest registered parent element of ev.target.
    start: function (ev, pointer) {
      if (this.state.isRunning) return;
      var parentTarget = this.getNearestParent(ev.target);
      // Get the options from the nearest registered parent
      var parentTargetOptions = parentTarget && parentTarget.$mdGesture[this.name] || {};

      this.state = {
        isRunning: true,
        // Override the default options with the nearest registered parent's options
        options: angular.extend({}, this.options, parentTargetOptions),
        // Pass in the registered parent node to the state so the onStart listener can use
        registeredParent: parentTarget
      };
      this.onStart(ev, pointer);
    },
    move: function (ev, pointer) {
      if (!this.state.isRunning) return;
      this.onMove(ev, pointer);
    },
    end: function (ev, pointer) {
      if (!this.state.isRunning) return;
      this.onEnd(ev, pointer);
      this.state.isRunning = false;
    },
    cancel: function (ev, pointer) {
      this.onCancel(ev, pointer);
      this.state = {};
    },

    // Find and return the nearest parent element that has been registered to
    // listen for this handler via $mdGesture.register(element, 'handlerName').
    getNearestParent: function (node) {
      var current = node;
      while (current) {
        if ((current.$mdGesture || {})[this.name]) {
          return current;
        }
        current = current.parentNode;
      }
      return null;
    },

    // Called from $mdGesture.register when an element registers itself with a handler.
    // Store the options the user gave on the DOMElement itself. These options will
    // be retrieved with getNearestParent when the handler starts.
    registerElement: function (element, options) {
      var self = this;
      element[0].$mdGesture = element[0].$mdGesture || {};
      element[0].$mdGesture[this.name] = options || {};
      element.on('$destroy', onDestroy);

      self.onSetup(element, options || {});

      return onDestroy;

      function onDestroy() {
        delete element[0].$mdGesture[self.name];
        element.off('$destroy', onDestroy);

        self.onCleanup(element, options || {});
      }
    }
  };

  return GestureHandler;

  /*
   * Dispatch an event with jQuery
   * TODO: Make sure this sends bubbling events
   *
   * @param srcEvent the original DOM touch event that started this.
   * @param eventType the name of the custom event to send (eg 'click' or '$md.drag')
   * @param eventPointer the pointer object that matches this event.
   */
  function jQueryDispatchEvent(srcEvent, eventType, eventPointer) {
    eventPointer = eventPointer || pointer;
    var eventObj = new angular.element.Event(eventType);

    eventObj.$material = true;
    eventObj.pointer = eventPointer;
    eventObj.srcEvent = srcEvent;

    angular.extend(eventObj, {
      clientX: eventPointer.x,
      clientY: eventPointer.y,
      screenX: eventPointer.x,
      screenY: eventPointer.y,
      pageX: eventPointer.x,
      pageY: eventPointer.y,
      ctrlKey: srcEvent.ctrlKey,
      altKey: srcEvent.altKey,
      shiftKey: srcEvent.shiftKey,
      metaKey: srcEvent.metaKey
    });
    angular.element(eventPointer.target).trigger(eventObj);
  }

  /*
   * NOTE: nativeDispatchEvent is very performance sensitive.
   * @param srcEvent the original DOM touch event that started this.
   * @param eventType the name of the custom event to send (eg 'click' or '$md.drag')
   * @param eventPointer the pointer object that matches this event.
   */
  function nativeDispatchEvent(srcEvent, eventType, eventPointer) {
    eventPointer = eventPointer || pointer;
    var eventObj;

    if (eventType === 'click' || eventType == 'mouseup' || eventType == 'mousedown' ) {
      eventObj = document.createEvent('MouseEvents');
      eventObj.initMouseEvent(
        eventType, true, true, window, srcEvent.detail,
        eventPointer.x, eventPointer.y, eventPointer.x, eventPointer.y,
        srcEvent.ctrlKey, srcEvent.altKey, srcEvent.shiftKey, srcEvent.metaKey,
        srcEvent.button, srcEvent.relatedTarget || null
      );

    } else {
      eventObj = document.createEvent('CustomEvent');
      eventObj.initCustomEvent(eventType, true, true, {});
    }
    eventObj.$material = true;
    eventObj.pointer = eventPointer;
    eventObj.srcEvent = srcEvent;
    eventPointer.target.dispatchEvent(eventObj);
  }

}

/**
 * Attach Gestures: hook document and check shouldHijack clicks
 * @ngInject
 */
function attachToDocument( $mdGesture, $$MdGestureHandler ) {

  // Polyfill document.contains for IE11.
  // TODO: move to util
  document.contains || (document.contains = function (node) {
    return document.body.contains(node);
  });

  if (!isInitialized && $mdGesture.isHijackingClicks ) {
    /*
     * If hijack clicks is true, we preventDefault any click that wasn't
     * sent by AngularJS Material. This is because on older Android & iOS, a false, or 'ghost',
     * click event will be sent ~400ms after a touchend event happens.
     * The only way to know if this click is real is to prevent any normal
     * click events, and add a flag to events sent by material so we know not to prevent those.
     *
     * Two exceptions to click events that should be prevented are:
     *  - click events sent by the keyboard (eg form submit)
     *  - events that originate from an Ionic app
     */
    document.addEventListener('click'    , clickHijacker     , true);
    document.addEventListener('mouseup'  , mouseInputHijacker, true);
    document.addEventListener('mousedown', mouseInputHijacker, true);
    document.addEventListener('focus'    , mouseInputHijacker, true);

    isInitialized = true;
  }

  function mouseInputHijacker(ev) {
    var isKeyClick = !ev.clientX && !ev.clientY;

    if (
      !isKeyClick &&
      !ev.$material &&
      !ev.isIonicTap &&
      !isInputEventFromLabelClick(ev) &&
      (ev.type !== 'mousedown' || (!canFocus(ev.target) && !canFocus(document.activeElement)))
    ) {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  function clickHijacker(ev) {
    var isKeyClick = ev.clientX === 0 && ev.clientY === 0;
    var isSubmitEvent = ev.target && ev.target.type === 'submit';
    if (!isKeyClick && !ev.$material && !ev.isIonicTap
      && !isInputEventFromLabelClick(ev)
      && !isSubmitEvent) {
      ev.preventDefault();
      ev.stopPropagation();
      lastLabelClickPos = null;
    } else {
      lastLabelClickPos = null;
      if (ev.target.tagName.toLowerCase() == 'label') {
        lastLabelClickPos = {x: ev.x, y: ev.y};
      }
    }
  }


  // Listen to all events to cover all platforms.
  var START_EVENTS = 'mousedown touchstart pointerdown';
  var MOVE_EVENTS = 'mousemove touchmove pointermove';
  var END_EVENTS = 'mouseup mouseleave touchend touchcancel pointerup pointercancel';

  angular.element(document)
    .on(START_EVENTS, gestureStart)
    .on(MOVE_EVENTS, gestureMove)
    .on(END_EVENTS, gestureEnd)
    // For testing
    .on('$$mdGestureReset', function gestureClearCache () {
      lastPointer = pointer = null;
    });

  /*
   * When a DOM event happens, run all registered gesture handlers' lifecycle
   * methods which match the DOM event.
   * Eg when a 'touchstart' event happens, runHandlers('start') will call and
   * run `handler.cancel()` and `handler.start()` on all registered handlers.
   */
  function runHandlers(handlerEvent, event) {
    var handler;
    for (var name in HANDLERS) {
      handler = HANDLERS[name];
      if( handler instanceof $$MdGestureHandler ) {

        if (handlerEvent === 'start') {
          // Run cancel to reset any handlers' state
          handler.cancel();
        }
        handler[handlerEvent](event, pointer);

      }
    }
  }

  /*
   * gestureStart vets if a start event is legitimate (and not part of a 'ghost click' from iOS/Android)
   * If it is legitimate, we initiate the pointer state and mark the current pointer's type
   * For example, for a touchstart event, mark the current pointer as a 'touch' pointer, so mouse events
   * won't effect it.
   */
  function gestureStart(ev) {
    // If we're already touched down, abort
    if (pointer) return;

    var now = +Date.now();

    // iOS & old android bug: after a touch event, a click event is sent 350 ms later.
    // If <400ms have passed, don't allow an event of a different type than the previous event
    if (lastPointer && !typesMatch(ev, lastPointer) && (now - lastPointer.endTime < 1500)) {
      return;
    }

    pointer = makeStartPointer(ev);

    runHandlers('start', ev);
  }
  /*
   * If a move event happens of the right type, update the pointer and run all the move handlers.
   * "of the right type": if a mousemove happens but our pointer started with a touch event, do nothing.
   */
  function gestureMove(ev) {
    if (!pointer || !typesMatch(ev, pointer)) return;

    updatePointerState(ev, pointer);
    runHandlers('move', ev);
  }
  /*
   * If an end event happens of the right type, update the pointer, run endHandlers, and save the pointer as 'lastPointer'
   */
  function gestureEnd(ev) {
    if (!pointer || !typesMatch(ev, pointer)) return;

    updatePointerState(ev, pointer);
    pointer.endTime = +Date.now();

    if (ev.type !== 'pointercancel') {
      runHandlers('end', ev);
    }

    lastPointer = pointer;
    pointer = null;
  }

}

// ********************
// Module Functions
// ********************

/*
 * Initiate the pointer. x, y, and the pointer's type.
 */
function makeStartPointer(ev) {
  var point = getEventPoint(ev);
  var startPointer = {
    startTime: +Date.now(),
    target: ev.target,
    // 'p' for pointer events, 'm' for mouse, 't' for touch
    type: ev.type.charAt(0)
  };
  startPointer.startX = startPointer.x = point.pageX;
  startPointer.startY = startPointer.y = point.pageY;
  return startPointer;
}

/*
 * return whether the pointer's type matches the event's type.
 * Eg if a touch event happens but the pointer has a mouse type, return false.
 */
function typesMatch(ev, pointer) {
  return ev && pointer && ev.type.charAt(0) === pointer.type;
}

/**
 * Gets whether the given event is an input event that was caused by clicking on an
 * associated label element.
 *
 * This is necessary because the browser will, upon clicking on a label element, fire an
 * *extra* click event on its associated input (if any). mdGesture is able to flag the label
 * click as with `$material` correctly, but not the second input click.
 *
 * In order to determine whether an input event is from a label click, we compare the (x, y) for
 * the event to the (x, y) for the most recent label click (which is cleared whenever a non-label
 * click occurs). Unfortunately, there are no event properties that tie the input and the label
 * together (such as relatedTarget).
 *
 * @param {MouseEvent} event
 * @returns {boolean}
 */
function isInputEventFromLabelClick(event) {
  return lastLabelClickPos
      && lastLabelClickPos.x == event.x
      && lastLabelClickPos.y == event.y;
}

/*
 * Update the given pointer based upon the given DOMEvent.
 * Distance, velocity, direction, duration, etc
 */
function updatePointerState(ev, pointer) {
  var point = getEventPoint(ev);
  var x = pointer.x = point.pageX;
  var y = pointer.y = point.pageY;

  pointer.distanceX = x - pointer.startX;
  pointer.distanceY = y - pointer.startY;
  pointer.distance = Math.sqrt(
    pointer.distanceX * pointer.distanceX + pointer.distanceY * pointer.distanceY
  );

  pointer.directionX = pointer.distanceX > 0 ? 'right' : pointer.distanceX < 0 ? 'left' : '';
  pointer.directionY = pointer.distanceY > 0 ? 'down' : pointer.distanceY < 0 ? 'up' : '';

  pointer.duration = +Date.now() - pointer.startTime;
  pointer.velocityX = pointer.distanceX / pointer.duration;
  pointer.velocityY = pointer.distanceY / pointer.duration;
}

/*
 * Normalize the point where the DOM event happened whether it's touch or mouse.
 * @returns point event obj with pageX and pageY on it.
 */
function getEventPoint(ev) {
  ev = ev.originalEvent || ev; // support jQuery events
  return (ev.touches && ev.touches[0]) ||
    (ev.changedTouches && ev.changedTouches[0]) ||
    ev;
}

/** Checks whether an element can be focused. */
function canFocus(element) {
  return (
    !!element &&
    element.getAttribute('tabindex') != '-1' &&
    !element.hasAttribute('disabled') &&
    (
      element.hasAttribute('tabindex') ||
      element.hasAttribute('href') ||
      element.isContentEditable ||
      ['INPUT', 'SELECT', 'BUTTON', 'TEXTAREA', 'VIDEO', 'AUDIO'].indexOf(element.nodeName) != -1
    )
  );
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.interaction
 * @description
 * User interaction detection to provide proper accessibility.
 */
MdInteractionService.$inject = ["$timeout", "$mdUtil"];
angular
  .module('material.core.interaction', [])
  .service('$mdInteraction', MdInteractionService);


/**
 * @ngdoc service
 * @name $mdInteraction
 * @module material.core.interaction
 *
 * @description
 *
 * Service which keeps track of the last interaction type and validates them for several browsers.
 * The service hooks into the document's body and listens for touch, mouse and keyboard events.
 *
 * The most recent interaction type can be retrieved by calling the `getLastInteractionType` method.
 *
 * Here is an example markup for using the interaction service.
 *
 * <hljs lang="js">
 *   var lastType = $mdInteraction.getLastInteractionType();
 *
 *   if (lastType === 'keyboard') {
 *     // We only restore the focus for keyboard users.
 *     restoreFocus();
 *   }
 * </hljs>
 *
 */
function MdInteractionService($timeout, $mdUtil) {
  this.$timeout = $timeout;
  this.$mdUtil = $mdUtil;

  this.bodyElement = angular.element(document.body);
  this.isBuffering = false;
  this.bufferTimeout = null;
  this.lastInteractionType = null;
  this.lastInteractionTime = null;

  // Type Mappings for the different events
  // There will be three three interaction types
  // `keyboard`, `mouse` and `touch`
  // type `pointer` will be evaluated in `pointerMap` for IE Browser events
  this.inputEventMap = {
    'keydown': 'keyboard',
    'mousedown': 'mouse',
    'mouseenter': 'mouse',
    'touchstart': 'touch',
    'pointerdown': 'pointer',
    'MSPointerDown': 'pointer'
  };

  // IE PointerDown events will be validated in `touch` or `mouse`
  // Index numbers referenced here: https://msdn.microsoft.com/library/windows/apps/hh466130.aspx
  this.iePointerMap = {
    2: 'touch',
    3: 'touch',
    4: 'mouse'
  };

  this.initializeEvents();
}

/**
 * Initializes the interaction service, by registering all interaction events to the
 * body element.
 */
MdInteractionService.prototype.initializeEvents = function() {
  // IE browsers can also trigger pointer events, which also leads to an interaction.
  var pointerEvent = 'MSPointerEvent' in window ? 'MSPointerDown' : 'PointerEvent' in window ? 'pointerdown' : null;

  this.bodyElement.on('keydown mousedown', this.onInputEvent.bind(this));

  if ('ontouchstart' in document.documentElement) {
    this.bodyElement.on('touchstart', this.onBufferInputEvent.bind(this));
  }

  if (pointerEvent) {
    this.bodyElement.on(pointerEvent, this.onInputEvent.bind(this));
  }

};

/**
 * Event listener for normal interaction events, which should be tracked.
 * @param event {MouseEvent|KeyboardEvent|PointerEvent|TouchEvent}
 */
MdInteractionService.prototype.onInputEvent = function(event) {
  if (this.isBuffering) {
    return;
  }

  var type = this.inputEventMap[event.type];

  if (type === 'pointer') {
    type = this.iePointerMap[event.pointerType] || event.pointerType;
  }

  this.lastInteractionType = type;
  this.lastInteractionTime = this.$mdUtil.now();
};

/**
 * Event listener for interaction events which should be buffered (touch events).
 * @param event {TouchEvent}
 */
MdInteractionService.prototype.onBufferInputEvent = function(event) {
  this.$timeout.cancel(this.bufferTimeout);

  this.onInputEvent(event);
  this.isBuffering = true;

  // The timeout of 650ms is needed to delay the touchstart, because otherwise the touch will call
  // the `onInput` function multiple times.
  this.bufferTimeout = this.$timeout(function() {
    this.isBuffering = false;
  }.bind(this), 650, false);

};

/**
 * @ngdoc method
 * @name $mdInteraction#getLastInteractionType
 * @description Retrieves the last interaction type triggered in body.
 * @returns {string|null} Last interaction type.
 */
MdInteractionService.prototype.getLastInteractionType = function() {
  return this.lastInteractionType;
};

/**
 * @ngdoc method
 * @name $mdInteraction#isUserInvoked
 * @description Method to detect whether any interaction happened recently or not.
 * @param {number=} checkDelay Time to check for any interaction to have been triggered.
 * @returns {boolean} Whether there was any interaction or not.
 */
MdInteractionService.prototype.isUserInvoked = function(checkDelay) {
  var delay = angular.isNumber(checkDelay) ? checkDelay : 15;

  // Check for any interaction to be within the specified check time.
  return this.lastInteractionTime >= this.$mdUtil.now() - delay;
};

})();
(function(){
"use strict";

angular.module('material.core')
  .provider('$$interimElement', InterimElementProvider);

/*
 * @ngdoc service
 * @name $$interimElement
 * @module material.core
 *
 * @description
 *
 * Factory that contructs `$$interimElement.$service` services.
 * Used internally in material design for elements that appear on screen temporarily.
 * The service provides a promise-like API for interacting with the temporary
 * elements.
 *
 * ```js
 * app.service('$mdToast', function($$interimElement) {
 *   var $mdToast = $$interimElement(toastDefaultOptions);
 *   return $mdToast;
 * });
 * ```
 * @param {object=} defaultOptions Options used by default for the `show` method on the service.
 *
 * @returns {$$interimElement.$service}
 *
 */

function InterimElementProvider() {
  InterimElementFactory.$inject = ["$document", "$q", "$rootScope", "$timeout", "$rootElement", "$animate", "$mdUtil", "$mdCompiler", "$mdTheming", "$injector", "$exceptionHandler"];
  createInterimElementProvider.$get = InterimElementFactory;
  return createInterimElementProvider;

  /**
   * Returns a new provider which allows configuration of a new interimElement
   * service. Allows configuration of default options & methods for options,
   * as well as configuration of 'preset' methods (eg dialog.basic(): basic is a preset method)
   */
  function createInterimElementProvider(interimFactoryName) {
    factory.$inject = ["$$interimElement", "$injector"];
    var EXPOSED_METHODS = ['onHide', 'onShow', 'onRemove'];

    var customMethods = {};
    var providerConfig = {
      presets: {}
    };

    var provider = {
      setDefaults: setDefaults,
      addPreset: addPreset,
      addMethod: addMethod,
      $get: factory
    };

    /**
     * all interim elements will come with the 'build' preset
     */
    provider.addPreset('build', {
      methods: ['controller', 'controllerAs', 'resolve', 'multiple',
        'template', 'templateUrl', 'themable', 'transformTemplate', 'parent', 'contentElement']
    });

    return provider;

    /**
     * Save the configured defaults to be used when the factory is instantiated
     */
    function setDefaults(definition) {
      providerConfig.optionsFactory = definition.options;
      providerConfig.methods = (definition.methods || []).concat(EXPOSED_METHODS);
      return provider;
    }

    /**
     * Add a method to the factory that isn't specific to any interim element operations
     */

    function addMethod(name, fn) {
      customMethods[name] = fn;
      return provider;
    }

    /**
     * Save the configured preset to be used when the factory is instantiated
     */
    function addPreset(name, definition) {
      definition = definition || {};
      definition.methods = definition.methods || [];
      definition.options = definition.options || function() { return {}; };

      if (/^cancel|hide|show$/.test(name)) {
        throw new Error("Preset '" + name + "' in " + interimFactoryName + " is reserved!");
      }
      if (definition.methods.indexOf('_options') > -1) {
        throw new Error("Method '_options' in " + interimFactoryName + " is reserved!");
      }
      providerConfig.presets[name] = {
        methods: definition.methods.concat(EXPOSED_METHODS),
        optionsFactory: definition.options,
        argOption: definition.argOption
      };
      return provider;
    }

    function addPresetMethod(presetName, methodName, method) {
      providerConfig.presets[presetName][methodName] = method;
    }

    /**
     * Create a factory that has the given methods & defaults implementing interimElement
     */
    /* @ngInject */
    function factory($$interimElement, $injector) {
      var defaultMethods;
      var defaultOptions;
      var interimElementService = $$interimElement();

      /*
       * publicService is what the developer will be using.
       * It has methods hide(), cancel(), show(), build(), and any other
       * presets which were set during the config phase.
       */
      var publicService = {
        hide: interimElementService.hide,
        cancel: interimElementService.cancel,
        show: showInterimElement,

        // Special internal method to destroy an interim element without animations
        // used when navigation changes causes a $scope.$destroy() action
        destroy : destroyInterimElement
      };


      defaultMethods = providerConfig.methods || [];
      // This must be invoked after the publicService is initialized
      defaultOptions = invokeFactory(providerConfig.optionsFactory, {});

      // Copy over the simple custom methods
      angular.forEach(customMethods, function(fn, name) {
        publicService[name] = fn;
      });

      angular.forEach(providerConfig.presets, function(definition, name) {
        var presetDefaults = invokeFactory(definition.optionsFactory, {});
        var presetMethods = (definition.methods || []).concat(defaultMethods);

        // Every interimElement built with a preset has a field called `$type`,
        // which matches the name of the preset.
        // Eg in preset 'confirm', options.$type === 'confirm'
        angular.extend(presetDefaults, { $type: name });

        // This creates a preset class which has setter methods for every
        // method given in the `.addPreset()` function, as well as every
        // method given in the `.setDefaults()` function.
        //
        // @example
        // .setDefaults({
        //   methods: ['hasBackdrop', 'clickOutsideToClose', 'escapeToClose', 'targetEvent'],
        //   options: dialogDefaultOptions
        // })
        // .addPreset('alert', {
        //   methods: ['title', 'ok'],
        //   options: alertDialogOptions
        // })
        //
        // Set values will be passed to the options when interimElement.show() is called.
        function Preset(opts) {
          this._options = angular.extend({}, presetDefaults, opts);
        }
        angular.forEach(presetMethods, function(name) {
          Preset.prototype[name] = function(value) {
            this._options[name] = value;
            return this;
          };
        });

        // Create shortcut method for one-linear methods
        if (definition.argOption) {
          var methodName = 'show' + name.charAt(0).toUpperCase() + name.slice(1);
          publicService[methodName] = function(arg) {
            var config = publicService[name](arg);
            return publicService.show(config);
          };
        }

        // eg $mdDialog.alert() will return a new alert preset
        publicService[name] = function(arg) {
          // If argOption is supplied, eg `argOption: 'content'`, then we assume
          // if the argument is not an options object then it is the `argOption` option.
          //
          // @example `$mdToast.simple('hello')` // sets options.content to hello
          //                                     // because argOption === 'content'
          if (arguments.length && definition.argOption &&
              !angular.isObject(arg) && !angular.isArray(arg))  {

            return (new Preset())[definition.argOption](arg);

          } else {
            return new Preset(arg);
          }

        };
      });

      return publicService;

      /**
       *
       */
      function showInterimElement(opts) {
        // opts is either a preset which stores its options on an _options field,
        // or just an object made up of options
        opts = opts || { };
        if (opts._options) opts = opts._options;

        return interimElementService.show(
          angular.extend({}, defaultOptions, opts)
        );
      }

      /**
       *  Special method to hide and destroy an interimElement WITHOUT
       *  any 'leave` or hide animations ( an immediate force hide/remove )
       *
       *  NOTE: This calls the onRemove() subclass method for each component...
       *  which must have code to respond to `options.$destroy == true`
       */
      function destroyInterimElement(opts) {
          return interimElementService.destroy(opts);
      }

      /**
       * Helper to call $injector.invoke with a local of the factory name for
       * this provider.
       * If an $mdDialog is providing options for a dialog and tries to inject
       * $mdDialog, a circular dependency error will happen.
       * We get around that by manually injecting $mdDialog as a local.
       */
      function invokeFactory(factory, defaultVal) {
        var locals = {};
        locals[interimFactoryName] = publicService;
        return $injector.invoke(factory || function() { return defaultVal; }, {}, locals);
      }

    }

  }

  /* @ngInject */
  function InterimElementFactory($document, $q, $rootScope, $timeout, $rootElement, $animate,
                                 $mdUtil, $mdCompiler, $mdTheming, $injector, $exceptionHandler) {
    return function createInterimElementService() {
      var SHOW_CANCELLED = false;

      /*
       * @ngdoc service
       * @name $$interimElement.$service
       *
       * @description
       * A service used to control inserting and removing an element into the DOM.
       *
       */

      var service;

      var showPromises = []; // Promises for the interim's which are currently opening.
      var hidePromises = []; // Promises for the interim's which are currently hiding.
      var showingInterims = []; // Interim elements which are currently showing up.

      // Publish instance $$interimElement service;
      // ... used as $mdDialog, $mdToast, $mdMenu, and $mdSelect

      return service = {
        show: show,
        hide: waitForInterim(hide),
        cancel: waitForInterim(cancel),
        destroy : destroy,
        $injector_: $injector
      };

      /*
       * @ngdoc method
       * @name $$interimElement.$service#show
       * @kind function
       *
       * @description
       * Adds the `$interimElement` to the DOM and returns a special promise that will be resolved or rejected
       * with hide or cancel, respectively. To external cancel/hide, developers should use the
       *
       * @param {*} options is hashMap of settings
       * @returns a Promise
       *
       */
      function show(options) {
        options = options || {};
        var interimElement = new InterimElement(options || {});

        // When an interim element is currently showing, we have to cancel it.
        // Just hiding it, will resolve the InterimElement's promise, the promise should be
        // rejected instead.
        var hideAction = options.multiple ? $q.resolve() : $q.all(showPromises);

        if (!options.multiple) {
          // Wait for all opening interim's to finish their transition.
          hideAction = hideAction.then(function() {
            // Wait for all closing and showing interim's to be completely closed.
            var promiseArray = hidePromises.concat(showingInterims.map(service.cancel));
            return $q.all(promiseArray);
          });
        }

        var showAction = hideAction.then(function() {

          return interimElement
            .show()
            .catch(function(reason) { return reason; })
            .finally(function() {
              showPromises.splice(showPromises.indexOf(showAction), 1);
              showingInterims.push(interimElement);
            });

        });

        showPromises.push(showAction);

        // In AngularJS 1.6+, exceptions inside promises will cause a rejection. We need to handle
        // the rejection and only log it if it's an error.
        interimElement.deferred.promise.catch(function(fault) {
          if (fault instanceof Error) {
            $exceptionHandler(fault);
          }

          return fault;
        });

        // Return a promise that will be resolved when the interim
        // element is hidden or cancelled...
        return interimElement.deferred.promise;
      }

      /*
       * @ngdoc method
       * @name $$interimElement.$service#hide
       * @kind function
       *
       * @description
       * Removes the `$interimElement` from the DOM and resolves the promise returned from `show`
       *
       * @param {*} resolveParam Data to resolve the promise with
       * @returns a Promise that will be resolved after the element has been removed.
       *
       */
      function hide(reason, options) {
        options = options || {};

        if (options.closeAll) {
          // We have to make a shallow copy of the array, because otherwise the map will break.
          return $q.all(showingInterims.slice().reverse().map(closeElement));
        } else if (options.closeTo !== undefined) {
          return $q.all(showingInterims.slice(options.closeTo).map(closeElement));
        }

        // Hide the latest showing interim element.
        return closeElement(showingInterims[showingInterims.length - 1]);

        function closeElement(interim) {

          var hideAction = interim
            .remove(reason, false, options || { })
            .catch(function(reason) { return reason; })
            .finally(function() {
              hidePromises.splice(hidePromises.indexOf(hideAction), 1);
            });

          showingInterims.splice(showingInterims.indexOf(interim), 1);
          hidePromises.push(hideAction);

          return interim.deferred.promise;
        }
      }

      /*
       * @ngdoc method
       * @name $$interimElement.$service#cancel
       * @kind function
       *
       * @description
       * Removes the `$interimElement` from the DOM and rejects the promise returned from `show`
       *
       * @param {*} reason Data to reject the promise with
       * @returns Promise that will be resolved after the element has been removed.
       *
       */
      function cancel(reason, options) {
        var interim = showingInterims.pop();
        if (!interim) {
          return $q.when(reason);
        }

        var cancelAction = interim
          .remove(reason, true, options || {})
          .catch(function(reason) { return reason; })
          .finally(function() {
            hidePromises.splice(hidePromises.indexOf(cancelAction), 1);
          });

        hidePromises.push(cancelAction);

        // Since AngularJS 1.6.7, promises will be logged to $exceptionHandler when the promise
        // is not handling the rejection. We create a pseudo catch handler, which will prevent the
        // promise from being logged to the $exceptionHandler.
        return interim.deferred.promise.catch(angular.noop);
      }

      /**
       * Creates a function to wait for at least one interim element to be available.
       * @param callbackFn Function to be used as callback
       * @returns {Function}
       */
      function waitForInterim(callbackFn) {
        return function() {
          var fnArguments = arguments;

          if (!showingInterims.length) {
            // When there are still interim's opening, then wait for the first interim element to
            // finish its open animation.
            if (showPromises.length) {
              return showPromises[0].finally(function () {
                return callbackFn.apply(service, fnArguments);
              });
            }

            return $q.when("No interim elements currently showing up.");
          }

          return callbackFn.apply(service, fnArguments);
        };
      }

      /*
       * Special method to quick-remove the interim element without animations
       * Note: interim elements are in "interim containers"
       */
      function destroy(targetEl) {
        var interim = !targetEl ? showingInterims.shift() : null;

        var parentEl = angular.element(targetEl).length && angular.element(targetEl)[0].parentNode;

        if (parentEl) {
          // Try to find the interim in the stack which corresponds to the supplied DOM element.
          var filtered = showingInterims.filter(function(entry) {
            return entry.options.element[0] === parentEl;
          });

          // Note: This function might be called when the element already has been removed,
          // in which case we won't find any matches.
          if (filtered.length) {
            interim = filtered[0];
            showingInterims.splice(showingInterims.indexOf(interim), 1);
          }
        }

        return interim ? interim.remove(SHOW_CANCELLED, false, { '$destroy': true }) :
                         $q.when(SHOW_CANCELLED);
      }

      /*
       * Internal Interim Element Object
       * Used internally to manage the DOM element and related data
       */
      function InterimElement(options) {
        var self, element, showAction = $q.when(true);

        options = configureScopeAndTransitions(options);

        return self = {
          options : options,
          deferred: $q.defer(),
          show    : createAndTransitionIn,
          remove  : transitionOutAndRemove
        };

        /**
         * Compile, link, and show this interim element
         * Use optional autoHided and transition-in effects
         */
        function createAndTransitionIn() {
          return $q(function(resolve, reject) {

            // Trigger onCompiling callback before the compilation starts.
            // This is useful, when modifying options, which can be influenced by developers.
            options.onCompiling && options.onCompiling(options);

            compileElement(options)
              .then(function( compiledData ) {
                element = linkElement( compiledData, options );

                // Expose the cleanup function from the compiler.
                options.cleanupElement = compiledData.cleanup;

                showAction = showElement(element, options, compiledData.controller)
                  .then(resolve, rejectAll);
              }).catch(rejectAll);

            function rejectAll(fault) {
              // Force the '$md<xxx>.show()' promise to reject
              self.deferred.reject(fault);

              // Continue rejection propagation
              reject(fault);
            }
          });
        }

        /**
         * After the show process has finished/rejected:
         * - announce 'removing',
         * - perform the transition-out, and
         * - perform optional clean up scope.
         */
        function transitionOutAndRemove(response, isCancelled, opts) {

          // abort if the show() and compile failed
          if ( !element ) return $q.when(false);

          options = angular.extend(options || {}, opts || {});
          options.cancelAutoHide && options.cancelAutoHide();
          options.element.triggerHandler('$mdInterimElementRemove');

          if ( options.$destroy === true ) {

            return hideElement(options.element, options).then(function(){
              (isCancelled && rejectAll(response)) || resolveAll(response);
            });

          } else {
            $q.when(showAction).finally(function() {
              hideElement(options.element, options).then(function() {
                isCancelled ? rejectAll(response) : resolveAll(response);
              }, rejectAll);
            });

            return self.deferred.promise;
          }


          /**
           * The `show()` returns a promise that will be resolved when the interim
           * element is hidden or cancelled...
           */
          function resolveAll(response) {
            self.deferred.resolve(response);
          }

          /**
           * Force the '$md<xxx>.show()' promise to reject
           */
          function rejectAll(fault) {
            self.deferred.reject(fault);
          }
        }

        /**
         * Prepare optional isolated scope and prepare $animate with default enter and leave
         * transitions for the new element instance.
         */
        function configureScopeAndTransitions(options) {
          options = options || { };
          if ( options.template ) {
            options.template = $mdUtil.processTemplate(options.template);
          }

          return angular.extend({
            preserveScope: false,
            cancelAutoHide : angular.noop,
            scope: options.scope || $rootScope.$new(options.isolateScope),

            /**
             * Default usage to enable $animate to transition-in; can be easily overridden via 'options'
             */
            onShow: function transitionIn(scope, element, options) {
              return $animate.enter(element, options.parent);
            },

            /**
             * Default usage to enable $animate to transition-out; can be easily overridden via 'options'
             */
            onRemove: function transitionOut(scope, element) {
              // Element could be undefined if a new element is shown before
              // the old one finishes compiling.
              return element && $animate.leave(element) || $q.when();
            }
          }, options );

        }

        /**
         * Compile an element with a templateUrl, controller, and locals
         */
        function compileElement(options) {

          var compiled = !options.skipCompile ? $mdCompiler.compile(options) : null;

          return compiled || $q(function (resolve) {
              resolve({
                locals: {},
                link: function () {
                  return options.element;
                }
              });
            });
        }

        /**
         *  Link an element with compiled configuration
         */
        function linkElement(compileData, options){
          angular.extend(compileData.locals, options);

          var element = compileData.link(options.scope);

          // Search for parent at insertion time, if not specified
          options.element = element;
          options.parent = findParent(element, options);
          if (options.themable) $mdTheming(element);

          return element;
        }

        /**
         * Search for parent at insertion time, if not specified
         */
        function findParent(element, options) {
          var parent = options.parent;

          // Search for parent at insertion time, if not specified
          if (angular.isFunction(parent)) {
            parent = parent(options.scope, element, options);
          } else if (angular.isString(parent)) {
            parent = angular.element($document[0].querySelector(parent));
          } else {
            parent = angular.element(parent);
          }

          // If parent querySelector/getter function fails, or it's just null,
          // find a default.
          if (!(parent || {}).length) {
            var el;
            if ($rootElement[0] && $rootElement[0].querySelector) {
              el = $rootElement[0].querySelector(':not(svg) > body');
            }
            if (!el) el = $rootElement[0];
            if (el.nodeName == '#comment') {
              el = $document[0].body;
            }
            return angular.element(el);
          }

          return parent;
        }

        /**
         * If auto-hide is enabled, start timer and prepare cancel function
         */
        function startAutoHide() {
          var autoHideTimer, cancelAutoHide = angular.noop;

          if (options.hideDelay) {
            autoHideTimer = $timeout(service.hide, options.hideDelay) ;
            cancelAutoHide = function() {
              $timeout.cancel(autoHideTimer);
            };
          }

          // Cache for subsequent use
          options.cancelAutoHide = function() {
            cancelAutoHide();
            options.cancelAutoHide = undefined;
          };
        }

        /**
         * Show the element ( with transitions), notify complete and start
         * optional auto-Hide
         */
        function showElement(element, options, controller) {
          // Trigger onShowing callback before the `show()` starts
          var notifyShowing = options.onShowing || angular.noop;
          // Trigger onComplete callback when the `show()` finishes
          var notifyComplete = options.onComplete || angular.noop;

          // Necessary for consistency between AngularJS 1.5 and 1.6.
          try {
            notifyShowing(options.scope, element, options, controller);
          } catch (e) {
            return $q.reject(e);
          }

          return $q(function (resolve, reject) {
            try {
              // Start transitionIn
              $q.when(options.onShow(options.scope, element, options, controller))
                .then(function () {
                  notifyComplete(options.scope, element, options);
                  startAutoHide();

                  resolve(element);
                }, reject);

            } catch (e) {
              reject(e.message);
            }
          });
        }

        function hideElement(element, options) {
          var announceRemoving = options.onRemoving || angular.noop;

          return $q(function (resolve, reject) {
            try {
              // Start transitionIn
              var action = $q.when( options.onRemove(options.scope, element, options) || true );

              // Trigger callback *before* the remove operation starts
              announceRemoving(element, action);

              if (options.$destroy) {
                // For $destroy, onRemove should be synchronous
                resolve(element);

                if (!options.preserveScope && options.scope ) {
                  // scope destroy should still be be done after the current digest is done
                  action.then( function() { options.scope.$destroy(); });
                }
              } else {
                // Wait until transition-out is done
                action.then(function () {
                  if (!options.preserveScope && options.scope ) {
                    options.scope.$destroy();
                  }

                  resolve(element);
                }, reject);
              }
            } catch (e) {
              reject(e.message);
            }
          });
        }

      }
    };

  }

}

})();
(function(){
"use strict";

(function() {
  'use strict';

  var $mdUtil, $interpolate, $log;

  var SUFFIXES = /(-gt)?-(sm|md|lg|print)/g;
  var WHITESPACE = /\s+/g;

  var FLEX_OPTIONS = ['grow', 'initial', 'auto', 'none', 'noshrink', 'nogrow' ];
  var LAYOUT_OPTIONS = ['row', 'column'];
  var ALIGNMENT_MAIN_AXIS= [ "", "start", "center", "end", "stretch", "space-around", "space-between" ];
  var ALIGNMENT_CROSS_AXIS= [ "", "start", "center", "end", "stretch" ];

  var config = {
    /**
     * Enable directive attribute-to-class conversions
     * Developers can use `<body md-layout-css />` to quickly
     * disable the Layout directives and prohibit the injection of Layout classNames
     */
    enabled: true,

    /**
     * List of mediaQuery breakpoints and associated suffixes
     *
     *   [
     *    { suffix: "sm", mediaQuery: "screen and (max-width: 599px)" },
     *    { suffix: "md", mediaQuery: "screen and (min-width: 600px) and (max-width: 959px)" }
     *   ]
     */
    breakpoints: []
  };

  registerLayoutAPI( angular.module('material.core.layout', ['ng']) );

  /**
   *   registerLayoutAPI()
   *
   *   The original AngularJS Material Layout solution used attribute selectors and CSS.
   *
   *  ```html
   *  <div layout="column"> My Content </div>
   *  ```
   *
   *  ```css
   *  [layout] {
   *    box-sizing: border-box;
   *    display:flex;
   *  }
   *  [layout=column] {
   *    flex-direction : column
   *  }
   *  ```
   *
   *  Use of attribute selectors creates significant performance impacts in some
   *  browsers... mainly IE.
   *
   *  This module registers directives that allow the same layout attributes to be
   *  interpreted and converted to class selectors. The directive will add equivalent classes to each element that
   *  contains a Layout directive.
   *
   * ```html
   *   <div layout="column" class="layout layout-column"> My Content </div>
   *```
   *
   *  ```css
   *  .layout {
   *    box-sizing: border-box;
   *    display:flex;
   *  }
   *  .layout-column {
   *    flex-direction : column
   *  }
   *  ```
   */
  function registerLayoutAPI(module){
    var PREFIX_REGEXP = /^((?:x|data)[\:\-_])/i;
    var SPECIAL_CHARS_REGEXP = /([\:\-\_]+(.))/g;

    // NOTE: these are also defined in constants::MEDIA_PRIORITY and constants::MEDIA
    var BREAKPOINTS     = [ "", "xs", "gt-xs", "sm", "gt-sm", "md", "gt-md", "lg", "gt-lg", "xl", "print" ];
    var API_WITH_VALUES = [ "layout", "flex", "flex-order", "flex-offset", "layout-align" ];
    var API_NO_VALUES   = [ "show", "hide", "layout-padding", "layout-margin" ];


    // Build directive registration functions for the standard Layout API... for all breakpoints.
    angular.forEach(BREAKPOINTS, function(mqb) {

      // Attribute directives with expected, observable value(s)
      angular.forEach( API_WITH_VALUES, function(name){
        var fullName = mqb ? name + "-" + mqb : name;
        module.directive( directiveNormalize(fullName), attributeWithObserve(fullName));
      });

      // Attribute directives with no expected value(s)
      angular.forEach( API_NO_VALUES, function(name){
        var fullName = mqb ? name + "-" + mqb : name;
        module.directive( directiveNormalize(fullName), attributeWithoutValue(fullName));
      });

    });

    // Register other, special directive functions for the Layout features:
    module

      .provider('$$mdLayout'     , function() {
        // Publish internal service for Layouts
        return {
          $get : angular.noop,
          validateAttributeValue : validateAttributeValue,
          validateAttributeUsage : validateAttributeUsage,
          /**
           * Easy way to disable/enable the Layout API.
           * When disabled, this stops all attribute-to-classname generations
           */
          disableLayouts  : function(isDisabled) {
            config.enabled =  (isDisabled !== true);
          }
        };
      })

      .directive('mdLayoutCss'        , disableLayoutDirective )
      .directive('ngCloak'            , buildCloakInterceptor('ng-cloak'))

      .directive('layoutWrap'   , attributeWithoutValue('layout-wrap'))
      .directive('layoutNowrap' , attributeWithoutValue('layout-nowrap'))
      .directive('layoutNoWrap' , attributeWithoutValue('layout-no-wrap'))
      .directive('layoutFill'   , attributeWithoutValue('layout-fill'))

      // !! Deprecated attributes: use the `-lt` (aka less-than) notations

      .directive('layoutLtMd'     , warnAttrNotSupported('layout-lt-md', true))
      .directive('layoutLtLg'     , warnAttrNotSupported('layout-lt-lg', true))
      .directive('flexLtMd'       , warnAttrNotSupported('flex-lt-md', true))
      .directive('flexLtLg'       , warnAttrNotSupported('flex-lt-lg', true))

      .directive('layoutAlignLtMd', warnAttrNotSupported('layout-align-lt-md'))
      .directive('layoutAlignLtLg', warnAttrNotSupported('layout-align-lt-lg'))
      .directive('flexOrderLtMd'  , warnAttrNotSupported('flex-order-lt-md'))
      .directive('flexOrderLtLg'  , warnAttrNotSupported('flex-order-lt-lg'))
      .directive('offsetLtMd'     , warnAttrNotSupported('flex-offset-lt-md'))
      .directive('offsetLtLg'     , warnAttrNotSupported('flex-offset-lt-lg'))

      .directive('hideLtMd'       , warnAttrNotSupported('hide-lt-md'))
      .directive('hideLtLg'       , warnAttrNotSupported('hide-lt-lg'))
      .directive('showLtMd'       , warnAttrNotSupported('show-lt-md'))
      .directive('showLtLg'       , warnAttrNotSupported('show-lt-lg'))

      // Determine if
      .config( detectDisabledLayouts );

    /**
     * Converts snake_case to camelCase.
     * Also there is special case for Moz prefix starting with upper case letter.
     * @param name Name to normalize
     */
    function directiveNormalize(name) {
      return name
        .replace(PREFIX_REGEXP, '')
        .replace(SPECIAL_CHARS_REGEXP, function(_, separator, letter, offset) {
          return offset ? letter.toUpperCase() : letter;
        });
    }

  }


  /**
    * Detect if any of the HTML tags has a [md-layouts-disabled] attribute;
    * If yes, then immediately disable all layout API features
    *
    * Note: this attribute should be specified on either the HTML or BODY tags
    */
   /**
    * @ngInject
    */
   function detectDisabledLayouts() {
     var isDisabled = !!document.querySelector('[md-layouts-disabled]');
     config.enabled = !isDisabled;
   }

  /**
   * Special directive that will disable ALL Layout conversions of layout
   * attribute(s) to classname(s).
   *
   * <link rel="stylesheet" href="angular-material.min.css">
   * <link rel="stylesheet" href="angular-material.layout.css">
   *
   * <body md-layout-css>
   *  ...
   * </body>
   *
   * Note: Using md-layout-css directive requires the developer to load the Material
   * Layout Attribute stylesheet (which only uses attribute selectors):
   *
   *       `angular-material.layout.css`
   *
   * Another option is to use the LayoutProvider to configure and disable the attribute
   * conversions; this would obviate the use of the `md-layout-css` directive
   *
   */
  function disableLayoutDirective() {
    // Return a 1x-only, first-match attribute directive
    config.enabled = false;

    return {
      restrict : 'A',
      priority : '900'
    };
  }

  /**
   * Tail-hook ngCloak to delay the uncloaking while Layout transformers
   * finish processing. Eliminates flicker with Material.Layouts
   */
  function buildCloakInterceptor(className) {
    return [ '$timeout', function($timeout){
      return {
        restrict : 'A',
        priority : -10,   // run after normal ng-cloak
        compile  : function( element ) {
          if (!config.enabled) return angular.noop;

          // Re-add the cloak
          element.addClass(className);

          return function( scope, element ) {
            // Wait while layout injectors configure, then uncloak
            // NOTE: $rAF does not delay enough... and this is a 1x-only event,
            //       $timeout is acceptable.
            $timeout( function(){
              element.removeClass(className);
            }, 10, false);
          };
        }
      };
    }];
  }


  // *********************************************************************************
  //
  // These functions create registration functions for AngularJS Material Layout attribute directives
  // This provides easy translation to switch AngularJS Material attribute selectors to
  // CLASS selectors and directives; which has huge performance implications
  // for IE Browsers
  //
  // *********************************************************************************

  /**
   * Creates a directive registration function where a possible dynamic attribute
   * value will be observed/watched.
   * @param {string} className attribute name; eg `layout-gt-md` with value ="row"
   */
  function attributeWithObserve(className) {

    return ['$mdUtil', '$interpolate', "$log", function(_$mdUtil_, _$interpolate_, _$log_) {
      $mdUtil = _$mdUtil_;
      $interpolate = _$interpolate_;
      $log = _$log_;

      return {
        restrict: 'A',
        compile: function(element, attr) {
          var linkFn;
          if (config.enabled) {
            // immediately replace static (non-interpolated) invalid values...

            validateAttributeUsage(className, attr, element, $log);

            validateAttributeValue( className,
              getNormalizedAttrValue(className, attr, ""),
              buildUpdateFn(element, className, attr)
            );

            linkFn = translateWithValueToCssClass;
          }

          // Use for postLink to account for transforms after ng-transclude.
          return linkFn || angular.noop;
        }
      };
    }];

    /**
     * Add as transformed class selector(s), then
     * remove the deprecated attribute selector
     */
    function translateWithValueToCssClass(scope, element, attrs) {
      var updateFn = updateClassWithValue(element, className, attrs);
      var unwatch = attrs.$observe(attrs.$normalize(className), updateFn);

      updateFn(getNormalizedAttrValue(className, attrs, ""));
      scope.$on("$destroy", function() { unwatch(); });
    }
  }

  /**
   * Creates a registration function for AngularJS Material Layout attribute directive.
   * This is a `simple` transpose of attribute usage to class usage; where we ignore
   * any attribute value
   */
  function attributeWithoutValue(className) {
    return ['$mdUtil', '$interpolate', "$log", function(_$mdUtil_, _$interpolate_, _$log_) {
      $mdUtil = _$mdUtil_;
      $interpolate = _$interpolate_;
      $log = _$log_;

      return {
        restrict: 'A',
        compile: function(element, attr) {
          var linkFn;
          if (config.enabled) {
            // immediately replace static (non-interpolated) invalid values...

            validateAttributeValue( className,
              getNormalizedAttrValue(className, attr, ""),
              buildUpdateFn(element, className, attr)
            );

            translateToCssClass(null, element);

            // Use for postLink to account for transforms after ng-transclude.
            linkFn = translateToCssClass;
          }

          return linkFn || angular.noop;
        }
      };
    }];

    /**
     * Add as transformed class selector, then
     * remove the deprecated attribute selector
     */
    function translateToCssClass(scope, element) {
      element.addClass(className);
    }
  }



  /**
   * After link-phase, do NOT remove deprecated layout attribute selector.
   * Instead watch the attribute so interpolated data-bindings to layout
   * selectors will continue to be supported.
   *
   * $observe() the className and update with new class (after removing the last one)
   *
   * e.g. `layout="{{layoutDemo.direction}}"` will update...
   *
   * NOTE: The value must match one of the specified styles in the CSS.
   * For example `flex-gt-md="{{size}}`  where `scope.size == 47` will NOT work since
   * only breakpoints for 0, 5, 10, 15... 100, 33, 34, 66, 67 are defined.
   *
   */
  function updateClassWithValue(element, className) {
    var lastClass;

    return function updateClassFn(newValue) {
      var value = validateAttributeValue(className, newValue || "");
      if ( angular.isDefined(value) ) {
        if (lastClass) element.removeClass(lastClass);
        lastClass = !value ? className : className + "-" + value.trim().replace(WHITESPACE, "-");
        element.addClass(lastClass);
      }
    };
  }

  /**
   * Provide console warning that this layout attribute has been deprecated
   *
   */
  function warnAttrNotSupported(className) {
    var parts = className.split("-");
    return ["$log", function($log) {
      $log.warn(className + "has been deprecated. Please use a `" + parts[0] + "-gt-<xxx>` variant.");
      return angular.noop;
    }];
  }

  /**
   * Centralize warnings for known flexbox issues (especially IE-related issues)
   */
  function validateAttributeUsage(className, attr, element, $log){
    var message, usage, url;
    var nodeName = element[0].nodeName.toLowerCase();

    switch(className.replace(SUFFIXES,"")) {
      case "flex":
        if ((nodeName == "md-button") || (nodeName == "fieldset")){
          // @see https://github.com/philipwalton/flexbugs#9-some-html-elements-cant-be-flex-containers
          // Use <div flex> wrapper inside (preferred) or outside

          usage = "<" + nodeName + " " + className + "></" + nodeName + ">";
          url = "https://github.com/philipwalton/flexbugs#9-some-html-elements-cant-be-flex-containers";
          message = "Markup '{0}' may not work as expected in IE Browsers. Consult '{1}' for details.";

          $log.warn( $mdUtil.supplant(message, [usage, url]) );
        }
    }

  }


  /**
   * For the Layout attribute value, validate or replace with default
   * fallback value
   */
  function validateAttributeValue(className, value, updateFn) {
    var origValue;

    if (!needsInterpolation(value)) {
      switch (className.replace(SUFFIXES,"")) {
        case 'layout'        :
          if ( !findIn(value, LAYOUT_OPTIONS) ) {
            value = LAYOUT_OPTIONS[0];    // 'row';
          }
          break;

        case 'flex'          :
          if (!findIn(value, FLEX_OPTIONS)) {
            if (isNaN(value)) {
              value = '';
            }
          }
          break;

        case 'flex-offset' :
        case 'flex-order'    :
          if (!value || isNaN(+value)) {
            value = '0';
          }
          break;

        case 'layout-align'  :
          var axis = extractAlignAxis(value);
          value = $mdUtil.supplant("{main}-{cross}",axis);
          break;

        case 'layout-padding' :
        case 'layout-margin'  :
        case 'layout-fill'    :
        case 'layout-wrap'    :
        case 'layout-nowrap'  :
        case 'layout-nowrap' :
          value = '';
          break;
      }

      if (value != origValue) {
        (updateFn || angular.noop)(value);
      }
    }

    return value ? value.trim() : "";
  }

  /**
   * Replace current attribute value with fallback value
   */
  function buildUpdateFn(element, className, attrs) {
    return function updateAttrValue(fallback) {
      if (!needsInterpolation(fallback)) {
        // Do not modify the element's attribute value; so
        // uses '<ui-layout layout="/api/sidebar.html" />' will not
        // be affected. Just update the attrs value.
        attrs[attrs.$normalize(className)] = fallback;
      }
    };
  }

  /**
   * See if the original value has interpolation symbols:
   * e.g.  flex-gt-md="{{triggerPoint}}"
   */
  function needsInterpolation(value) {
    return (value || "").indexOf($interpolate.startSymbol()) > -1;
  }

  function getNormalizedAttrValue(className, attrs, defaultVal) {
    var normalizedAttr = attrs.$normalize(className);
    return attrs[normalizedAttr] ? attrs[normalizedAttr].trim().replace(WHITESPACE, "-") : defaultVal || null;
  }

  function findIn(item, list, replaceWith) {
    item = replaceWith && item ? item.replace(WHITESPACE, replaceWith) : item;

    var found = false;
    if (item) {
      list.forEach(function(it) {
        it = replaceWith ? it.replace(WHITESPACE, replaceWith) : it;
        found = found || (it === item);
      });
    }
    return found;
  }

  function extractAlignAxis(attrValue) {
    var axis = {
      main : "start",
      cross: "stretch"
    }, values;

    attrValue = (attrValue || "");

    if ( attrValue.indexOf("-") === 0 || attrValue.indexOf(" ") === 0) {
      // For missing main-axis values
      attrValue = "none" + attrValue;
    }

    values = attrValue.toLowerCase().trim().replace(WHITESPACE, "-").split("-");
    if ( values.length && (values[0] === "space") ) {
      // for main-axis values of "space-around" or "space-between"
      values = [ values[0]+"-"+values[1],values[2] ];
    }

    if ( values.length > 0 ) axis.main  = values[0] || axis.main;
    if ( values.length > 1 ) axis.cross = values[1] || axis.cross;

    if ( ALIGNMENT_MAIN_AXIS.indexOf(axis.main) < 0 )   axis.main = "start";
    if ( ALIGNMENT_CROSS_AXIS.indexOf(axis.cross) < 0 ) axis.cross = "stretch";

    return axis;
  }


})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.liveannouncer
 * @description
 * AngularJS Material Live Announcer to provide accessibility for Voice Readers.
 */
MdLiveAnnouncer.$inject = ["$timeout"];
angular
  .module('material.core')
  .service('$mdLiveAnnouncer', MdLiveAnnouncer);

/**
 * @ngdoc service
 * @name $mdLiveAnnouncer
 * @module material.core.liveannouncer
 *
 * @description
 *
 * Service to announce messages to supported screenreaders.
 *
 * > The `$mdLiveAnnouncer` service is internally used for components to provide proper accessibility.
 *
 * <hljs lang="js">
 *   module.controller('AppCtrl', function($mdLiveAnnouncer) {
 *     // Basic announcement (Polite Mode)
 *     $mdLiveAnnouncer.announce('Hey Google');
 *
 *     // Custom announcement (Assertive Mode)
 *     $mdLiveAnnouncer.announce('Hey Google', 'assertive');
 *   });
 * </hljs>
 *
 */
function MdLiveAnnouncer($timeout) {
  /** @private @const @type {!angular.$timeout} */
  this._$timeout = $timeout;

  /** @private @const @type {!HTMLElement} */
  this._liveElement = this._createLiveElement();

  /** @private @const @type {!number} */
  this._announceTimeout = 100;
}

/**
 * @ngdoc method
 * @name $mdLiveAnnouncer#announce
 * @description Announces messages to supported screenreaders.
 * @param {string} message Message to be announced to the screenreader
 * @param {'off'|'polite'|'assertive'} politeness The politeness of the announcer element.
 */
MdLiveAnnouncer.prototype.announce = function(message, politeness) {
  if (!politeness) {
    politeness = 'polite';
  }

  var self = this;

  self._liveElement.textContent = '';
  self._liveElement.setAttribute('aria-live', politeness);

  // This 100ms timeout is necessary for some browser + screen-reader combinations:
  // - Both JAWS and NVDA over IE11 will not announce anything without a non-zero timeout.
  // - With Chrome and IE11 with NVDA or JAWS, a repeated (identical) message won't be read a
  //   second time without clearing and then using a non-zero delay.
  // (using JAWS 17 at time of this writing).
  self._$timeout(function() {
    self._liveElement.textContent = message;
  }, self._announceTimeout, false);
};

/**
 * Creates a live announcer element, which listens for DOM changes and announces them
 * to the screenreaders.
 * @returns {!HTMLElement}
 * @private
 */
MdLiveAnnouncer.prototype._createLiveElement = function() {
  var liveEl = document.createElement('div');

  liveEl.classList.add('md-visually-hidden');
  liveEl.setAttribute('role', 'status');
  liveEl.setAttribute('aria-atomic', 'true');
  liveEl.setAttribute('aria-live', 'polite');

  document.body.appendChild(liveEl);

  return liveEl;
};

})();
(function(){
"use strict";

/**
 * @ngdoc service
 * @name $$mdMeta
 * @module material.core.meta
 *
 * @description
 *
 * A provider and a service that simplifies meta tags access
 *
 * Note: This is intended only for use with dynamic meta tags such as browser color and title.
 * Tags that are only processed when the page is rendered (such as `charset`, and `http-equiv`)
 * will not work since `$$mdMeta` adds the tags after the page has already been loaded.
 *
 * ```js
 * app.config(function($$mdMetaProvider) {
 *   var removeMeta = $$mdMetaProvider.setMeta('meta-name', 'content');
 *   var metaValue  = $$mdMetaProvider.getMeta('meta-name'); // -> 'content'
 *
 *   removeMeta();
 * });
 *
 * app.controller('myController', function($$mdMeta) {
 *   var removeMeta = $$mdMeta.setMeta('meta-name', 'content');
 *   var metaValue  = $$mdMeta.getMeta('meta-name'); // -> 'content'
 *
 *   removeMeta();
 * });
 * ```
 *
 * @returns {$$mdMeta.$service}
 *
 */
angular.module('material.core.meta', [])
  .provider('$$mdMeta', function () {
    var head = angular.element(document.head);
    var metaElements = {};

    /**
     * Checks if the requested element was written manually and maps it
     *
     * @param {string} name meta tag 'name' attribute value
     * @returns {boolean} returns true if there is an element with the requested name
     */
    function mapExistingElement(name) {
      if (metaElements[name]) {
        return true;
      }

      var element = document.getElementsByName(name)[0];

      if (!element) {
        return false;
      }

      metaElements[name] = angular.element(element);

      return true;
    }

    /**
     * @ngdoc method
     * @name $$mdMeta#setMeta
     *
     * @description
     * Creates meta element with the 'name' and 'content' attributes,
     * if the meta tag is already created than we replace the 'content' value
     *
     * @param {string} name meta tag 'name' attribute value
     * @param {string} content meta tag 'content' attribute value
     * @returns {function} remove function
     *
     */
    function setMeta(name, content) {
      mapExistingElement(name);

      if (!metaElements[name]) {
        var newMeta = angular.element('<meta name="' + name + '" content="' + content + '"/>');
        head.append(newMeta);
        metaElements[name] = newMeta;
      }
      else {
        metaElements[name].attr('content', content);
      }

      return function () {
        metaElements[name].attr('content', '');
        metaElements[name].remove();
        delete metaElements[name];
      };
    }

    /**
     * @ngdoc method
     * @name $$mdMeta#getMeta
     *
     * @description
     * Gets the 'content' attribute value of the wanted meta element
     *
     * @param {string} name meta tag 'name' attribute value
     * @returns {string} content attribute value
     */
    function getMeta(name) {
      if (!mapExistingElement(name)) {
        throw Error('$$mdMeta: could not find a meta tag with the name \'' + name + '\'');
      }

      return metaElements[name].attr('content');
    }

    var module = {
      setMeta: setMeta,
      getMeta: getMeta
    };

    return angular.extend({}, module, {
      $get: function () {
        return module;
      }
    });
  });
})();
(function(){
"use strict";

  /**
   * @ngdoc module
   * @name material.core.componentRegistry
   *
   * @description
   * A component instance registration service.
   * Note: currently this as a private service in the SideNav component.
   */
  ComponentRegistry.$inject = ["$log", "$q"];
  angular.module('material.core')
    .factory('$mdComponentRegistry', ComponentRegistry);

  /*
   * @private
   * @ngdoc factory
   * @name ComponentRegistry
   * @module material.core.componentRegistry
   *
   */
  function ComponentRegistry($log, $q) {

    var self;
    var instances = [ ];
    var pendings = { };

    return self = {
      /**
       * Used to print an error when an instance for a handle isn't found.
       */
      notFoundError: function(handle, msgContext) {
        $log.error( (msgContext || "") + 'No instance found for handle', handle);
      },
      /**
       * Return all registered instances as an array.
       */
      getInstances: function() {
        return instances;
      },

      /**
       * Get a registered instance.
       * @param handle the String handle to look up for a registered instance.
       */
      get: function(handle) {
        if ( !isValidID(handle) ) return null;

        var i, j, instance;
        for(i = 0, j = instances.length; i < j; i++) {
          instance = instances[i];
          if(instance.$$mdHandle === handle) {
            return instance;
          }
        }
        return null;
      },

      /**
       * Register an instance.
       * @param instance the instance to register
       * @param handle the handle to identify the instance under.
       */
      register: function(instance, handle) {
        if ( !handle ) return angular.noop;

        instance.$$mdHandle = handle;
        instances.push(instance);
        resolveWhen();

        return deregister;

        /**
         * Remove registration for an instance
         */
        function deregister() {
          var index = instances.indexOf(instance);
          if (index !== -1) {
            instances.splice(index, 1);
          }
        }

        /**
         * Resolve any pending promises for this instance
         */
        function resolveWhen() {
          var dfd = pendings[handle];
          if ( dfd ) {
            dfd.forEach(function (promise) {
              promise.resolve(instance);
            });
            delete pendings[handle];
          }
        }
      },

      /**
       * Async accessor to registered component instance
       * If not available then a promise is created to notify
       * all listeners when the instance is registered.
       */
      when : function(handle) {
        if ( isValidID(handle) ) {
          var deferred = $q.defer();
          var instance = self.get(handle);

          if ( instance )  {
            deferred.resolve( instance );
          } else {
            if (pendings[handle] === undefined) {
              pendings[handle] = [];
            }
            pendings[handle].push(deferred);
          }

          return deferred.promise;
        }
        return $q.reject("Invalid `md-component-id` value.");
      }

    };

    function isValidID(handle){
      return handle && (handle !== "");
    }

  }

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdButtonInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-button.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the default ripple configuration
   */

  MdButtonInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdButtonInkRipple', MdButtonInkRipple);

  function MdButtonInkRipple($mdInkRipple) {
    return {
      attach: function attachRipple(scope, element, options) {
        options = angular.extend(optionsForElement(element), options);

        return $mdInkRipple.attach(scope, element, options);
      }
    };

    function optionsForElement(element) {
      if (element.hasClass('md-icon-button')) {
        return {
          isMenuItem: element.hasClass('md-menu-item'),
          fitRipple: true,
          center: true
        };
      } else {
        return {
          isMenuItem: element.hasClass('md-menu-item'),
          dimBackground: true
        };
      }
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

    /**
   * @ngdoc service
   * @name $mdCheckboxInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-checkbox.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdCheckboxInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdCheckboxInkRipple', MdCheckboxInkRipple);

  function MdCheckboxInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: true,
        dimBackground: false,
        fitRipple: true
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdListInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-list.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdListInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdListInkRipple', MdListInkRipple);

  function MdListInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: false,
        dimBackground: true,
        outline: false,
        rippleSize: 'full'
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.core.ripple
 * @description
 * Ripple
 */
InkRippleCtrl.$inject = ["$scope", "$element", "rippleOptions", "$window", "$timeout", "$mdUtil", "$mdColorUtil"];
InkRippleDirective.$inject = ["$mdButtonInkRipple", "$mdCheckboxInkRipple"];
angular.module('material.core')
    .provider('$mdInkRipple', InkRippleProvider)
    .directive('mdInkRipple', InkRippleDirective)
    .directive('mdNoInk', attrNoDirective)
    .directive('mdNoBar', attrNoDirective)
    .directive('mdNoStretch', attrNoDirective);

var DURATION = 450;

/**
 * @ngdoc directive
 * @name mdInkRipple
 * @module material.core.ripple
 *
 * @description
 * The `md-ink-ripple` directive allows you to specify the ripple color or if a ripple is allowed.
 *
 * @param {string|boolean} md-ink-ripple A color string `#FF0000` or boolean (`false` or `0`) for preventing ripple
 *
 * @usage
 * ### String values
 * <hljs lang="html">
 *   <ANY md-ink-ripple="#FF0000">
 *     Ripples in red
 *   </ANY>
 *
 *   <ANY md-ink-ripple="false">
 *     Not rippling
 *   </ANY>
 * </hljs>
 *
 * ### Interpolated values
 * <hljs lang="html">
 *   <ANY md-ink-ripple="{{ randomColor() }}">
 *     Ripples with the return value of 'randomColor' function
 *   </ANY>
 *
 *   <ANY md-ink-ripple="{{ canRipple() }}">
 *     Ripples if 'canRipple' function return value is not 'false' or '0'
 *   </ANY>
 * </hljs>
 */
function InkRippleDirective ($mdButtonInkRipple, $mdCheckboxInkRipple) {
  return {
    controller: angular.noop,
    link:       function (scope, element, attr) {
      attr.hasOwnProperty('mdInkRippleCheckbox')
          ? $mdCheckboxInkRipple.attach(scope, element)
          : $mdButtonInkRipple.attach(scope, element);
    }
  };
}

/**
 * @ngdoc service
 * @name $mdInkRipple
 * @module material.core.ripple
 *
 * @description
 * `$mdInkRipple` is a service for adding ripples to any element
 *
 * @usage
 * <hljs lang="js">
 * app.factory('$myElementInkRipple', function($mdInkRipple) {
 *   return {
 *     attach: function (scope, element, options) {
 *       return $mdInkRipple.attach(scope, element, angular.extend({
 *         center: false,
 *         dimBackground: true
 *       }, options));
 *     }
 *   };
 * });
 *
 * app.controller('myController', function ($scope, $element, $myElementInkRipple) {
 *   $scope.onClick = function (ev) {
 *     $myElementInkRipple.attach($scope, angular.element(ev.target), { center: true });
 *   }
 * });
 * </hljs>
 *
 * ### Disabling ripples globally
 * If you want to disable ink ripples globally, for all components, you can call the
 * `disableInkRipple` method in your app's config.
 *
 * <hljs lang="js">
 * app.config(function ($mdInkRippleProvider) {
 *   $mdInkRippleProvider.disableInkRipple();
 * });
 */

function InkRippleProvider () {
  var isDisabledGlobally = false;

  return {
    disableInkRipple: disableInkRipple,
    $get: ["$injector", function($injector) {
      return { attach: attach };

      /**
       * @ngdoc method
       * @name $mdInkRipple#attach
       *
       * @description
       * Attaching given scope, element and options to inkRipple controller
       *
       * @param {object=} scope Scope within the current context
       * @param {object=} element The element the ripple effect should be applied to
       * @param {object=} options (Optional) Configuration options to override the defaultRipple configuration
       * * `center` -  Whether the ripple should start from the center of the container element
       * * `dimBackground` - Whether the background should be dimmed with the ripple color
       * * `colorElement` - The element the ripple should take its color from, defined by css property `color`
       * * `fitRipple` - Whether the ripple should fill the element
       */
      function attach (scope, element, options) {
        if (isDisabledGlobally || element.controller('mdNoInk')) return angular.noop;
        return $injector.instantiate(InkRippleCtrl, {
          $scope:        scope,
          $element:      element,
          rippleOptions: options
        });
      }
    }]
  };

  /**
   * @ngdoc method
   * @name $mdInkRipple#disableInkRipple
   *
   * @description
   * A config-time method that, when called, disables ripples globally.
   */
  function disableInkRipple () {
    isDisabledGlobally = true;
  }
}

/**
 * Controller used by the ripple service in order to apply ripples
 * @ngInject
 */
function InkRippleCtrl ($scope, $element, rippleOptions, $window, $timeout, $mdUtil, $mdColorUtil) {
  this.$window    = $window;
  this.$timeout   = $timeout;
  this.$mdUtil    = $mdUtil;
  this.$mdColorUtil    = $mdColorUtil;
  this.$scope     = $scope;
  this.$element   = $element;
  this.options    = rippleOptions;
  this.mousedown  = false;
  this.ripples    = [];
  this.timeout    = null; // Stores a reference to the most-recent ripple timeout
  this.lastRipple = null;

  $mdUtil.valueOnUse(this, 'container', this.createContainer);

  this.$element.addClass('md-ink-ripple');

  // attach method for unit tests
  ($element.controller('mdInkRipple') || {}).createRipple = angular.bind(this, this.createRipple);
  ($element.controller('mdInkRipple') || {}).setColor = angular.bind(this, this.color);

  this.bindEvents();
}


/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (either by
 * mouseup or mouseleave event)
 */
function autoCleanup (self, cleanupFn) {

  if ( self.mousedown || self.lastRipple ) {
    self.mousedown = false;
    self.$mdUtil.nextTick( angular.bind(self, cleanupFn), false);
  }

}


/**
 * Returns the color that the ripple should be (either based on CSS or hard-coded)
 * @returns {string}
 */
InkRippleCtrl.prototype.color = function (value) {
  var self = this;

  // If assigning a color value, apply it to background and the ripple color
  if (angular.isDefined(value)) {
    self._color = self._parseColor(value);
  }

  // If color lookup, use assigned, defined, or inherited
  return self._color || self._parseColor( self.inkRipple() ) || self._parseColor( getElementColor() );

  /**
   * Finds the color element and returns its text color for use as default ripple color
   * @returns {string}
   */
  function getElementColor () {
    var items = self.options && self.options.colorElement ? self.options.colorElement : [];
    var elem =  items.length ? items[ 0 ] : self.$element[ 0 ];

    return elem ? self.$window.getComputedStyle(elem).color : 'rgb(0,0,0)';
  }
};

/**
 * Updating the ripple colors based on the current inkRipple value
 * or the element's computed style color
 */
InkRippleCtrl.prototype.calculateColor = function () {
  return this.color();
};


/**
 * Takes a string color and converts it to RGBA format
 * @param color {string}
 * @param [multiplier] {int}
 * @returns {string}
 */

InkRippleCtrl.prototype._parseColor = function parseColor (color, multiplier) {
  multiplier = multiplier || 1;
  var colorUtil = this.$mdColorUtil;

  if (!color) return;
  if (color.indexOf('rgba') === 0) return color.replace(/\d?\.?\d*\s*\)\s*$/, (0.1 * multiplier).toString() + ')');
  if (color.indexOf('rgb') === 0) return colorUtil.rgbToRgba(color);
  if (color.indexOf('#') === 0) return colorUtil.hexToRgba(color);

};

/**
 * Binds events to the root element for
 */
InkRippleCtrl.prototype.bindEvents = function () {
  this.$element.on('mousedown', angular.bind(this, this.handleMousedown));
  this.$element.on('mouseup touchend', angular.bind(this, this.handleMouseup));
  this.$element.on('mouseleave', angular.bind(this, this.handleMouseup));
  this.$element.on('touchmove', angular.bind(this, this.handleTouchmove));
};

/**
 * Create a new ripple on every mousedown event from the root element
 * @param event {MouseEvent}
 */
InkRippleCtrl.prototype.handleMousedown = function (event) {
  if ( this.mousedown ) return;

  // When jQuery is loaded, we have to get the original event
  if (event.hasOwnProperty('originalEvent')) event = event.originalEvent;
  this.mousedown = true;
  if (this.options.center) {
    this.createRipple(this.container.prop('clientWidth') / 2, this.container.prop('clientWidth') / 2);
  } else {

    // We need to calculate the relative coordinates if the target is a sublayer of the ripple element
    if (event.srcElement !== this.$element[0]) {
      var layerRect = this.$element[0].getBoundingClientRect();
      var layerX = event.clientX - layerRect.left;
      var layerY = event.clientY - layerRect.top;

      this.createRipple(layerX, layerY);
    } else {
      this.createRipple(event.offsetX, event.offsetY);
    }
  }
};

/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (either by
 * mouseup, touchend or mouseleave event)
 */
InkRippleCtrl.prototype.handleMouseup = function () {
  autoCleanup(this, this.clearRipples);
};

/**
 * Either remove or unlock any remaining ripples when the user mouses off of the element (by
 * touchmove)
 */
InkRippleCtrl.prototype.handleTouchmove = function () {
  autoCleanup(this, this.deleteRipples);
};

/**
 * Cycles through all ripples and attempts to remove them.
 */
InkRippleCtrl.prototype.deleteRipples = function () {
  for (var i = 0; i < this.ripples.length; i++) {
    this.ripples[ i ].remove();
  }
};

/**
 * Cycles through all ripples and attempts to remove them with fade.
 * Depending on logic within `fadeInComplete`, some removals will be postponed.
 */
InkRippleCtrl.prototype.clearRipples = function () {
  for (var i = 0; i < this.ripples.length; i++) {
    this.fadeInComplete(this.ripples[ i ]);
  }
};

/**
 * Creates the ripple container element
 * @returns {*}
 */
InkRippleCtrl.prototype.createContainer = function () {
  var container = angular.element('<div class="md-ripple-container"></div>');
  this.$element.append(container);
  return container;
};

InkRippleCtrl.prototype.clearTimeout = function () {
  if (this.timeout) {
    this.$timeout.cancel(this.timeout);
    this.timeout = null;
  }
};

InkRippleCtrl.prototype.isRippleAllowed = function () {
  var element = this.$element[0];
  do {
    if (!element.tagName || element.tagName === 'BODY') break;

    if (element && angular.isFunction(element.hasAttribute)) {
      if (element.hasAttribute('disabled')) return false;
      if (this.inkRipple() === 'false' || this.inkRipple() === '0') return false;
    }

  } while (element = element.parentNode);
  return true;
};

/**
 * The attribute `md-ink-ripple` may be a static or interpolated
 * color value OR a boolean indicator (used to disable ripples)
 */
InkRippleCtrl.prototype.inkRipple = function () {
  return this.$element.attr('md-ink-ripple');
};

/**
 * Creates a new ripple and adds it to the container.  Also tracks ripple in `this.ripples`.
 * @param left
 * @param top
 */
InkRippleCtrl.prototype.createRipple = function (left, top) {
  if (!this.isRippleAllowed()) return;

  var ctrl        = this;
  var colorUtil   = ctrl.$mdColorUtil;
  var ripple      = angular.element('<div class="md-ripple"></div>');
  var width       = this.$element.prop('clientWidth');
  var height      = this.$element.prop('clientHeight');
  var x           = Math.max(Math.abs(width - left), left) * 2;
  var y           = Math.max(Math.abs(height - top), top) * 2;
  var size        = getSize(this.options.fitRipple, x, y);
  var color       = this.calculateColor();

  ripple.css({
    left:            left + 'px',
    top:             top + 'px',
    background:      'black',
    width:           size + 'px',
    height:          size + 'px',
    backgroundColor: colorUtil.rgbaToRgb(color),
    borderColor:     colorUtil.rgbaToRgb(color)
  });
  this.lastRipple = ripple;

  // we only want one timeout to be running at a time
  this.clearTimeout();
  this.timeout    = this.$timeout(function () {
    ctrl.clearTimeout();
    if (!ctrl.mousedown) ctrl.fadeInComplete(ripple);
  }, DURATION * 0.35, false);

  if (this.options.dimBackground) this.container.css({ backgroundColor: color });
  this.container.append(ripple);
  this.ripples.push(ripple);
  ripple.addClass('md-ripple-placed');

  this.$mdUtil.nextTick(function () {

    ripple.addClass('md-ripple-scaled md-ripple-active');
    ctrl.$timeout(function () {
      ctrl.clearRipples();
    }, DURATION, false);

  }, false);

  function getSize (fit, x, y) {
    return fit
        ? Math.max(x, y)
        : Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
  }
};



/**
 * After fadeIn finishes, either kicks off the fade-out animation or queues the element for removal on mouseup
 * @param ripple
 */
InkRippleCtrl.prototype.fadeInComplete = function (ripple) {
  if (this.lastRipple === ripple) {
    if (!this.timeout && !this.mousedown) {
      this.removeRipple(ripple);
    }
  } else {
    this.removeRipple(ripple);
  }
};

/**
 * Kicks off the animation for removing a ripple
 * @param ripple {Element}
 */
InkRippleCtrl.prototype.removeRipple = function (ripple) {
  var ctrl  = this;
  var index = this.ripples.indexOf(ripple);
  if (index < 0) return;
  this.ripples.splice(this.ripples.indexOf(ripple), 1);
  ripple.removeClass('md-ripple-active');
  ripple.addClass('md-ripple-remove');
  if (this.ripples.length === 0) this.container.css({ backgroundColor: '' });
  // use a 2-second timeout in order to allow for the animation to finish
  // we don't actually care how long the animation takes
  this.$timeout(function () {
    ctrl.fadeOutComplete(ripple);
  }, DURATION, false);
};

/**
 * Removes the provided ripple from the DOM
 * @param ripple
 */
InkRippleCtrl.prototype.fadeOutComplete = function (ripple) {
  ripple.remove();
  this.lastRipple = null;
};

/**
 * Used to create an empty directive.  This is used to track flag-directives whose children may have
 * functionality based on them.
 *
 * Example: `md-no-ink` will potentially be used by all child directives.
 */
function attrNoDirective () {
  return { controller: angular.noop };
}

})();
(function(){
"use strict";

(function() {
  'use strict';

    /**
   * @ngdoc service
   * @name $mdTabInkRipple
   * @module material.core
   *
   * @description
   * Provides ripple effects for md-tabs.  See $mdInkRipple service for all possible configuration options.
   *
   * @param {object=} scope Scope within the current context
   * @param {object=} element The element the ripple effect should be applied to
   * @param {object=} options (Optional) Configuration options to override the defaultripple configuration
   */

  MdTabInkRipple.$inject = ["$mdInkRipple"];
  angular.module('material.core')
    .factory('$mdTabInkRipple', MdTabInkRipple);

  function MdTabInkRipple($mdInkRipple) {
    return {
      attach: attach
    };

    function attach(scope, element, options) {
      return $mdInkRipple.attach(scope, element, angular.extend({
        center: false,
        dimBackground: true,
        outline: false,
        rippleSize: 'full'
      }, options));
    }
  }
})();

})();
(function(){
"use strict";

angular.module('material.core.theming.palette', [])
.constant('$mdColorPalette', {
  'red': {
    '50': '#ffebee',
    '100': '#ffcdd2',
    '200': '#ef9a9a',
    '300': '#e57373',
    '400': '#ef5350',
    '500': '#f44336',
    '600': '#e53935',
    '700': '#d32f2f',
    '800': '#c62828',
    '900': '#b71c1c',
    'A100': '#ff8a80',
    'A200': '#ff5252',
    'A400': '#ff1744',
    'A700': '#d50000',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 A100',
    'contrastStrongLightColors': '400 500 600 700 A200 A400 A700'
  },
  'pink': {
    '50': '#fce4ec',
    '100': '#f8bbd0',
    '200': '#f48fb1',
    '300': '#f06292',
    '400': '#ec407a',
    '500': '#e91e63',
    '600': '#d81b60',
    '700': '#c2185b',
    '800': '#ad1457',
    '900': '#880e4f',
    'A100': '#ff80ab',
    'A200': '#ff4081',
    'A400': '#f50057',
    'A700': '#c51162',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '500 600 A200 A400 A700'
  },
  'purple': {
    '50': '#f3e5f5',
    '100': '#e1bee7',
    '200': '#ce93d8',
    '300': '#ba68c8',
    '400': '#ab47bc',
    '500': '#9c27b0',
    '600': '#8e24aa',
    '700': '#7b1fa2',
    '800': '#6a1b9a',
    '900': '#4a148c',
    'A100': '#ea80fc',
    'A200': '#e040fb',
    'A400': '#d500f9',
    'A700': '#aa00ff',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200 A400 A700'
  },
  'deep-purple': {
    '50': '#ede7f6',
    '100': '#d1c4e9',
    '200': '#b39ddb',
    '300': '#9575cd',
    '400': '#7e57c2',
    '500': '#673ab7',
    '600': '#5e35b1',
    '700': '#512da8',
    '800': '#4527a0',
    '900': '#311b92',
    'A100': '#b388ff',
    'A200': '#7c4dff',
    'A400': '#651fff',
    'A700': '#6200ea',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200'
  },
  'indigo': {
    '50': '#e8eaf6',
    '100': '#c5cae9',
    '200': '#9fa8da',
    '300': '#7986cb',
    '400': '#5c6bc0',
    '500': '#3f51b5',
    '600': '#3949ab',
    '700': '#303f9f',
    '800': '#283593',
    '900': '#1a237e',
    'A100': '#8c9eff',
    'A200': '#536dfe',
    'A400': '#3d5afe',
    'A700': '#304ffe',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100',
    'contrastStrongLightColors': '300 400 A200 A400'
  },
  'blue': {
    '50': '#e3f2fd',
    '100': '#bbdefb',
    '200': '#90caf9',
    '300': '#64b5f6',
    '400': '#42a5f5',
    '500': '#2196f3',
    '600': '#1e88e5',
    '700': '#1976d2',
    '800': '#1565c0',
    '900': '#0d47a1',
    'A100': '#82b1ff',
    'A200': '#448aff',
    'A400': '#2979ff',
    'A700': '#2962ff',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 400 A100',
    'contrastStrongLightColors': '500 600 700 A200 A400 A700'
  },
  'light-blue': {
    '50': '#e1f5fe',
    '100': '#b3e5fc',
    '200': '#81d4fa',
    '300': '#4fc3f7',
    '400': '#29b6f6',
    '500': '#03a9f4',
    '600': '#039be5',
    '700': '#0288d1',
    '800': '#0277bd',
    '900': '#01579b',
    'A100': '#80d8ff',
    'A200': '#40c4ff',
    'A400': '#00b0ff',
    'A700': '#0091ea',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '600 700 800 900 A700',
    'contrastStrongLightColors': '600 700 800 A700'
  },
  'cyan': {
    '50': '#e0f7fa',
    '100': '#b2ebf2',
    '200': '#80deea',
    '300': '#4dd0e1',
    '400': '#26c6da',
    '500': '#00bcd4',
    '600': '#00acc1',
    '700': '#0097a7',
    '800': '#00838f',
    '900': '#006064',
    'A100': '#84ffff',
    'A200': '#18ffff',
    'A400': '#00e5ff',
    'A700': '#00b8d4',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '700 800 900',
    'contrastStrongLightColors': '700 800 900'
  },
  'teal': {
    '50': '#e0f2f1',
    '100': '#b2dfdb',
    '200': '#80cbc4',
    '300': '#4db6ac',
    '400': '#26a69a',
    '500': '#009688',
    '600': '#00897b',
    '700': '#00796b',
    '800': '#00695c',
    '900': '#004d40',
    'A100': '#a7ffeb',
    'A200': '#64ffda',
    'A400': '#1de9b6',
    'A700': '#00bfa5',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '500 600 700 800 900',
    'contrastStrongLightColors': '500 600 700'
  },
  'green': {
    '50': '#e8f5e9',
    '100': '#c8e6c9',
    '200': '#a5d6a7',
    '300': '#81c784',
    '400': '#66bb6a',
    '500': '#4caf50',
    '600': '#43a047',
    '700': '#388e3c',
    '800': '#2e7d32',
    '900': '#1b5e20',
    'A100': '#b9f6ca',
    'A200': '#69f0ae',
    'A400': '#00e676',
    'A700': '#00c853',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '500 600 700 800 900',
    'contrastStrongLightColors': '500 600 700'
  },
  'light-green': {
    '50': '#f1f8e9',
    '100': '#dcedc8',
    '200': '#c5e1a5',
    '300': '#aed581',
    '400': '#9ccc65',
    '500': '#8bc34a',
    '600': '#7cb342',
    '700': '#689f38',
    '800': '#558b2f',
    '900': '#33691e',
    'A100': '#ccff90',
    'A200': '#b2ff59',
    'A400': '#76ff03',
    'A700': '#64dd17',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '700 800 900',
    'contrastStrongLightColors': '700 800 900'
  },
  'lime': {
    '50': '#f9fbe7',
    '100': '#f0f4c3',
    '200': '#e6ee9c',
    '300': '#dce775',
    '400': '#d4e157',
    '500': '#cddc39',
    '600': '#c0ca33',
    '700': '#afb42b',
    '800': '#9e9d24',
    '900': '#827717',
    'A100': '#f4ff81',
    'A200': '#eeff41',
    'A400': '#c6ff00',
    'A700': '#aeea00',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '900',
    'contrastStrongLightColors': '900'
  },
  'yellow': {
    '50': '#fffde7',
    '100': '#fff9c4',
    '200': '#fff59d',
    '300': '#fff176',
    '400': '#ffee58',
    '500': '#ffeb3b',
    '600': '#fdd835',
    '700': '#fbc02d',
    '800': '#f9a825',
    '900': '#f57f17',
    'A100': '#ffff8d',
    'A200': '#ffff00',
    'A400': '#ffea00',
    'A700': '#ffd600',
    'contrastDefaultColor': 'dark'
  },
  'amber': {
    '50': '#fff8e1',
    '100': '#ffecb3',
    '200': '#ffe082',
    '300': '#ffd54f',
    '400': '#ffca28',
    '500': '#ffc107',
    '600': '#ffb300',
    '700': '#ffa000',
    '800': '#ff8f00',
    '900': '#ff6f00',
    'A100': '#ffe57f',
    'A200': '#ffd740',
    'A400': '#ffc400',
    'A700': '#ffab00',
    'contrastDefaultColor': 'dark'
  },
  'orange': {
    '50': '#fff3e0',
    '100': '#ffe0b2',
    '200': '#ffcc80',
    '300': '#ffb74d',
    '400': '#ffa726',
    '500': '#ff9800',
    '600': '#fb8c00',
    '700': '#f57c00',
    '800': '#ef6c00',
    '900': '#e65100',
    'A100': '#ffd180',
    'A200': '#ffab40',
    'A400': '#ff9100',
    'A700': '#ff6d00',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '800 900',
    'contrastStrongLightColors': '800 900'
  },
  'deep-orange': {
    '50': '#fbe9e7',
    '100': '#ffccbc',
    '200': '#ffab91',
    '300': '#ff8a65',
    '400': '#ff7043',
    '500': '#ff5722',
    '600': '#f4511e',
    '700': '#e64a19',
    '800': '#d84315',
    '900': '#bf360c',
    'A100': '#ff9e80',
    'A200': '#ff6e40',
    'A400': '#ff3d00',
    'A700': '#dd2c00',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 400 A100 A200',
    'contrastStrongLightColors': '500 600 700 800 900 A400 A700'
  },
  'brown': {
    '50': '#efebe9',
    '100': '#d7ccc8',
    '200': '#bcaaa4',
    '300': '#a1887f',
    '400': '#8d6e63',
    '500': '#795548',
    '600': '#6d4c41',
    '700': '#5d4037',
    '800': '#4e342e',
    '900': '#3e2723',
    'A100': '#d7ccc8',
    'A200': '#bcaaa4',
    'A400': '#8d6e63',
    'A700': '#5d4037',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 A100 A200',
    'contrastStrongLightColors': '300 400'
  },
  'grey': {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#eeeeee',
    '300': '#e0e0e0',
    '400': '#bdbdbd',
    '500': '#9e9e9e',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
    'A100': '#ffffff',
    'A200': '#000000',
    'A400': '#303030',
    'A700': '#616161',
    'contrastDefaultColor': 'dark',
    'contrastLightColors': '600 700 800 900 A200 A400 A700'
  },
  'blue-grey': {
    '50': '#eceff1',
    '100': '#cfd8dc',
    '200': '#b0bec5',
    '300': '#90a4ae',
    '400': '#78909c',
    '500': '#607d8b',
    '600': '#546e7a',
    '700': '#455a64',
    '800': '#37474f',
    '900': '#263238',
    'A100': '#cfd8dc',
    'A200': '#b0bec5',
    'A400': '#78909c',
    'A700': '#455a64',
    'contrastDefaultColor': 'light',
    'contrastDarkColors': '50 100 200 300 A100 A200',
    'contrastStrongLightColors': '400 500 700'
  }
});

})();
(function(){
"use strict";

(function(angular) {
  'use strict';
/**
 * @ngdoc module
 * @name material.core.theming
 * @description
 * Theming
 */
detectDisabledThemes.$inject = ["$mdThemingProvider"];
ThemingDirective.$inject = ["$mdTheming", "$interpolate", "$parse", "$mdUtil", "$q", "$log"];
ThemableDirective.$inject = ["$mdTheming"];
ThemingProvider.$inject = ["$mdColorPalette", "$$mdMetaProvider"];
generateAllThemes.$inject = ["$injector", "$mdTheming"];
angular.module('material.core.theming', ['material.core.theming.palette', 'material.core.meta'])
  .directive('mdTheme', ThemingDirective)
  .directive('mdThemable', ThemableDirective)
  .directive('mdThemesDisabled', disableThemesDirective )
  .provider('$mdTheming', ThemingProvider)
  .config( detectDisabledThemes )
  .run(generateAllThemes);

/**
 * Detect if the HTML or the BODY tags has a [md-themes-disabled] attribute
 * If yes, then immediately disable all theme stylesheet generation and DOM injection
 */
/**
 * @ngInject
 */
function detectDisabledThemes($mdThemingProvider) {
  var isDisabled = !!document.querySelector('[md-themes-disabled]');
  $mdThemingProvider.disableTheming(isDisabled);
}

/**
 * @ngdoc service
 * @name $mdThemingProvider
 * @module material.core.theming
 *
 * @description Provider to configure the `$mdTheming` service.
 *
 * ### Default Theme
 * The `$mdThemingProvider` uses by default the following theme configuration:
 *
 * - Primary Palette: `Blue`
 * - Accent Palette: `Pink`
 * - Warn Palette: `Deep-Orange`
 * - Background Palette: `Grey`
 *
 * If you don't want to use the `md-theme` directive on the elements itself, you may want to overwrite
 * the default theme.<br/>
 * This can be done by using the following markup.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     $mdThemingProvider
 *       .theme('default')
 *       .primaryPalette('blue')
 *       .accentPalette('teal')
 *       .warnPalette('red')
 *       .backgroundPalette('grey');
 *   });
 * </hljs>
 *

 * ### Dynamic Themes
 *
 * By default, if you change a theme at runtime, the `$mdTheming` service will not detect those changes.<br/>
 * If you have an application, which changes its theme on runtime, you have to enable theme watching.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Enable theme watching.
 *     $mdThemingProvider.alwaysWatchTheme(true);
 *   });
 * </hljs>
 *
 * ### Custom Theme Styles
 *
 * Sometimes you may want to use your own theme styles for some custom components.<br/>
 * You are able to register your own styles by using the following markup.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Register our custom stylesheet into the theming provider.
 *     $mdThemingProvider.registerStyles(STYLESHEET);
 *   });
 * </hljs>
 *
 * The `registerStyles` method only accepts strings as value, so you're actually not able to load an external
 * stylesheet file into the `$mdThemingProvider`.
 *
 * If it's necessary to load an external stylesheet, we suggest using a bundler, which supports including raw content,
 * like [raw-loader](https://github.com/webpack/raw-loader) for `webpack`.
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Register your custom stylesheet into the theming provider.
 *     $mdThemingProvider.registerStyles(require('../styles/my-component.theme.css'));
 *   });
 * </hljs>
 *
 * ### Browser color
 *
 * Enables browser header coloring
 * for more info please visit:
 * https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color
 *
 * Options parameter: <br/>
 * `theme`   - A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme. <br/>
 * `palette` - Can be any one of the basic material design palettes, extended defined palettes and 'primary',
 *             'accent', 'background' and 'warn'. Default is `primary`. <br/>
 * `hue`     - The hue from the selected palette. Default is `800`<br/>
 *
 * <hljs lang="js">
 *   myAppModule.config(function($mdThemingProvider) {
 *     // Enable browser color
 *     $mdThemingProvider.enableBrowserColor({
 *       theme: 'myTheme', // Default is 'default'
 *       palette: 'accent', // Default is 'primary', any basic material palette and extended palettes are available
 *       hue: '200' // Default is '800'
 *     });
 *   });
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdThemingProvider#registerStyles
 * @param {string} styles The styles to be appended to AngularJS Material's built in theme css.
 */
/**
 * @ngdoc method
 * @name $mdThemingProvider#setNonce
 * @param {string} nonceValue The nonce to be added as an attribute to the theme style tags.
 * Setting a value allows the use of CSP policy without using the unsafe-inline directive.
 */

/**
 * @ngdoc method
 * @name $mdThemingProvider#setDefaultTheme
 * @param {string} themeName Default theme name to be applied to elements. Default value is `default`.
 */

/**
 * @ngdoc method
 * @name $mdThemingProvider#alwaysWatchTheme
 * @param {boolean} watch Whether or not to always watch themes for changes and re-apply
 * classes when they change. Default is `false`. Enabling can reduce performance.
 */

/**
 * @ngdoc method
 * @name $mdThemingProvider#enableBrowserColor
 * @param {Object=} options Options object for the browser color<br/>
 * `theme`   - A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme. <br/>
 * `palette` - Can be any one of the basic material design palettes, extended defined palettes and 'primary',
 *             'accent', 'background' and 'warn'. Default is `primary`. <br/>
 * `hue`     - The hue from the selected palette. Default is `800`<br/>
 * @returns {Function} remove function of the browser color
 */

/* Some Example Valid Theming Expressions
 * =======================================
 *
 * Intention group expansion: (valid for primary, accent, warn, background)
 *
 * {{primary-100}} - grab shade 100 from the primary palette
 * {{primary-100-0.7}} - grab shade 100, apply opacity of 0.7
 * {{primary-100-contrast}} - grab shade 100's contrast color
 * {{primary-hue-1}} - grab the shade assigned to hue-1 from the primary palette
 * {{primary-hue-1-0.7}} - apply 0.7 opacity to primary-hue-1
 * {{primary-color}} - Generates .md-hue-1, .md-hue-2, .md-hue-3 with configured shades set for each hue
 * {{primary-color-0.7}} - Apply 0.7 opacity to each of the above rules
 * {{primary-contrast}} - Generates .md-hue-1, .md-hue-2, .md-hue-3 with configured contrast (ie. text) color shades set for each hue
 * {{primary-contrast-0.7}} - Apply 0.7 opacity to each of the above rules
 *
 * Foreground expansion: Applies rgba to black/white foreground text
 *
 * {{foreground-1}} - used for primary text
 * {{foreground-2}} - used for secondary text/divider
 * {{foreground-3}} - used for disabled text
 * {{foreground-4}} - used for dividers
 *
 */

// In memory generated CSS rules; registered by theme.name
var GENERATED = { };

// In memory storage of defined themes and color palettes (both loaded by CSS, and user specified)
var PALETTES;

// Text Colors on light and dark backgrounds
// @see https://www.google.com/design/spec/style/color.html#color-text-background-colors
var DARK_FOREGROUND = {
  name: 'dark',
  '1': 'rgba(0,0,0,0.87)',
  '2': 'rgba(0,0,0,0.54)',
  '3': 'rgba(0,0,0,0.38)',
  '4': 'rgba(0,0,0,0.12)'
};
var LIGHT_FOREGROUND = {
  name: 'light',
  '1': 'rgba(255,255,255,1.0)',
  '2': 'rgba(255,255,255,0.7)',
  '3': 'rgba(255,255,255,0.5)',
  '4': 'rgba(255,255,255,0.12)'
};

var DARK_SHADOW = '1px 1px 0px rgba(0,0,0,0.4), -1px -1px 0px rgba(0,0,0,0.4)';
var LIGHT_SHADOW = '';

var DARK_CONTRAST_COLOR = colorToRgbaArray('rgba(0,0,0,0.87)');
var LIGHT_CONTRAST_COLOR = colorToRgbaArray('rgba(255,255,255,0.87)');
var STRONG_LIGHT_CONTRAST_COLOR = colorToRgbaArray('rgb(255,255,255)');

var THEME_COLOR_TYPES = ['primary', 'accent', 'warn', 'background'];
var DEFAULT_COLOR_TYPE = 'primary';

// A color in a theme will use these hues by default, if not specified by user.
var LIGHT_DEFAULT_HUES = {
  'accent': {
    'default': 'A200',
    'hue-1': 'A100',
    'hue-2': 'A400',
    'hue-3': 'A700'
  },
  'background': {
    'default': '50',
    'hue-1': 'A100',
    'hue-2': '100',
    'hue-3': '300'
  }
};

var DARK_DEFAULT_HUES = {
  'background': {
    'default': 'A400',
    'hue-1': '800',
    'hue-2': '900',
    'hue-3': 'A200'
  }
};
THEME_COLOR_TYPES.forEach(function(colorType) {
  // Color types with unspecified default hues will use these default hue values
  var defaultDefaultHues = {
    'default': '500',
    'hue-1': '300',
    'hue-2': '800',
    'hue-3': 'A100'
  };
  if (!LIGHT_DEFAULT_HUES[colorType]) LIGHT_DEFAULT_HUES[colorType] = defaultDefaultHues;
  if (!DARK_DEFAULT_HUES[colorType]) DARK_DEFAULT_HUES[colorType] = defaultDefaultHues;
});

var VALID_HUE_VALUES = [
  '50', '100', '200', '300', '400', '500', '600',
  '700', '800', '900', 'A100', 'A200', 'A400', 'A700'
];

var themeConfig = {
  disableTheming : false,   // Generate our themes at run time; also disable stylesheet DOM injection
  generateOnDemand : false, // Whether or not themes are to be generated on-demand (vs. eagerly).
  registeredStyles : [],    // Custom styles registered to be used in the theming of custom components.
  nonce : null              // Nonce to be added as an attribute to the generated themes style tags.
};

/**
 *
 */
function ThemingProvider($mdColorPalette, $$mdMetaProvider) {
  ThemingService.$inject = ["$rootScope", "$mdUtil", "$q", "$log"];
  PALETTES = { };
  var THEMES = { };

  var themingProvider;

  var alwaysWatchTheme = false;
  var defaultTheme = 'default';

  // Load JS Defined Palettes
  angular.extend(PALETTES, $mdColorPalette);

  // Default theme defined in core.js

  /**
   * Adds `theme-color` and `msapplication-navbutton-color` meta tags with the color parameter
   * @param {string} color Hex value of the wanted browser color
   * @returns {Function} Remove function of the meta tags
   */
  var setBrowserColor = function (color) {
    // Chrome, Firefox OS and Opera
    var removeChrome = $$mdMetaProvider.setMeta('theme-color', color);
    // Windows Phone
    var removeWindows = $$mdMetaProvider.setMeta('msapplication-navbutton-color', color);

    return function () {
      removeChrome();
      removeWindows();
    };
  };

  /**
   * Enables browser header coloring
   * for more info please visit:
   * https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color
   *
   * The default color is `800` from `primary` palette of the `default` theme
   *
   * options are:
   * `theme`   - A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme
   * `palette` - Can be any one of the basic material design palettes, extended defined palettes and 'primary',
   *             'accent', 'background' and 'warn'. Default is `primary`
   * `hue`     - The hue from the selected palette. Default is `800`
   *
   * @param {Object=} options Options object for the browser color
   * @returns {Function} remove function of the browser color
   */
  var enableBrowserColor = function (options) {
    options = angular.isObject(options) ? options : {};

    var theme = options.theme || 'default';
    var hue = options.hue || '800';

    var palette = PALETTES[options.palette] ||
      PALETTES[THEMES[theme].colors[options.palette || 'primary'].name];

    var color = angular.isObject(palette[hue]) ? palette[hue].hex : palette[hue];

    return setBrowserColor(color);
  };

  return themingProvider = {
    definePalette: definePalette,
    extendPalette: extendPalette,
    theme: registerTheme,

    /**
     * return a read-only clone of the current theme configuration
     */
    configuration : function() {
      return angular.extend( { }, themeConfig, {
        defaultTheme : defaultTheme,
        alwaysWatchTheme : alwaysWatchTheme,
        registeredStyles : [].concat(themeConfig.registeredStyles)
      });
    },

    /**
     * Easy way to disable theming without having to use
     * `.constant("$MD_THEME_CSS","");` This disables
     * all dynamic theme style sheet generations and injections...
     */
    disableTheming: function(isDisabled) {
      themeConfig.disableTheming = angular.isUndefined(isDisabled) || !!isDisabled;
    },

    registerStyles: function(styles) {
      themeConfig.registeredStyles.push(styles);
    },

    setNonce: function(nonceValue) {
      themeConfig.nonce = nonceValue;
    },

    generateThemesOnDemand: function(onDemand) {
      themeConfig.generateOnDemand = onDemand;
    },

    setDefaultTheme: function(theme) {
      defaultTheme = theme;
    },

    alwaysWatchTheme: function(alwaysWatch) {
      alwaysWatchTheme = alwaysWatch;
    },

    enableBrowserColor: enableBrowserColor,

    $get: ThemingService,
    _LIGHT_DEFAULT_HUES: LIGHT_DEFAULT_HUES,
    _DARK_DEFAULT_HUES: DARK_DEFAULT_HUES,
    _PALETTES: PALETTES,
    _THEMES: THEMES,
    _parseRules: parseRules,
    _rgba: rgba
  };

  // Example: $mdThemingProvider.definePalette('neonRed', { 50: '#f5fafa', ... });
  function definePalette(name, map) {
    map = map || {};
    PALETTES[name] = checkPaletteValid(name, map);
    return themingProvider;
  }

  // Returns an new object which is a copy of a given palette `name` with variables from
  // `map` overwritten
  // Example: var neonRedMap = $mdThemingProvider.extendPalette('red', { 50: '#f5fafafa' });
  function extendPalette(name, map) {
    return checkPaletteValid(name,  angular.extend({}, PALETTES[name] || {}, map) );
  }

  // Make sure that palette has all required hues
  function checkPaletteValid(name, map) {
    var missingColors = VALID_HUE_VALUES.filter(function(field) {
      return !map[field];
    });
    if (missingColors.length) {
      throw new Error("Missing colors %1 in palette %2!"
                      .replace('%1', missingColors.join(', '))
                      .replace('%2', name));
    }

    return map;
  }

  // Register a theme (which is a collection of color palettes to use with various states
  // ie. warn, accent, primary )
  // Optionally inherit from an existing theme
  // $mdThemingProvider.theme('custom-theme').primaryPalette('red');
  function registerTheme(name, inheritFrom) {
    if (THEMES[name]) return THEMES[name];

    inheritFrom = inheritFrom || 'default';

    var parentTheme = typeof inheritFrom === 'string' ? THEMES[inheritFrom] : inheritFrom;
    var theme = new Theme(name);

    if (parentTheme) {
      angular.forEach(parentTheme.colors, function(color, colorType) {
        theme.colors[colorType] = {
          name: color.name,
          // Make sure a COPY of the hues is given to the child color,
          // not the same reference.
          hues: angular.extend({}, color.hues)
        };
      });
    }
    THEMES[name] = theme;

    return theme;
  }

  function Theme(name) {
    var self = this;
    self.name = name;
    self.colors = {};

    self.dark = setDark;
    setDark(false);

    function setDark(isDark) {
      isDark = arguments.length === 0 ? true : !!isDark;

      // If no change, abort
      if (isDark === self.isDark) return;

      self.isDark = isDark;

      self.foregroundPalette = self.isDark ? LIGHT_FOREGROUND : DARK_FOREGROUND;
      self.foregroundShadow = self.isDark ? DARK_SHADOW : LIGHT_SHADOW;

      // Light and dark themes have different default hues.
      // Go through each existing color type for this theme, and for every
      // hue value that is still the default hue value from the previous light/dark setting,
      // set it to the default hue value from the new light/dark setting.
      var newDefaultHues = self.isDark ? DARK_DEFAULT_HUES : LIGHT_DEFAULT_HUES;
      var oldDefaultHues = self.isDark ? LIGHT_DEFAULT_HUES : DARK_DEFAULT_HUES;
      angular.forEach(newDefaultHues, function(newDefaults, colorType) {
        var color = self.colors[colorType];
        var oldDefaults = oldDefaultHues[colorType];
        if (color) {
          for (var hueName in color.hues) {
            if (color.hues[hueName] === oldDefaults[hueName]) {
              color.hues[hueName] = newDefaults[hueName];
            }
          }
        }
      });

      return self;
    }

    THEME_COLOR_TYPES.forEach(function(colorType) {
      var defaultHues = (self.isDark ? DARK_DEFAULT_HUES : LIGHT_DEFAULT_HUES)[colorType];
      self[colorType + 'Palette'] = function setPaletteType(paletteName, hues) {
        var color = self.colors[colorType] = {
          name: paletteName,
          hues: angular.extend({}, defaultHues, hues)
        };

        Object.keys(color.hues).forEach(function(name) {
          if (!defaultHues[name]) {
            throw new Error("Invalid hue name '%1' in theme %2's %3 color %4. Available hue names: %4"
              .replace('%1', name)
              .replace('%2', self.name)
              .replace('%3', paletteName)
              .replace('%4', Object.keys(defaultHues).join(', '))
            );
          }
        });
        Object.keys(color.hues).map(function(key) {
          return color.hues[key];
        }).forEach(function(hueValue) {
          if (VALID_HUE_VALUES.indexOf(hueValue) == -1) {
            throw new Error("Invalid hue value '%1' in theme %2's %3 color %4. Available hue values: %5"
              .replace('%1', hueValue)
              .replace('%2', self.name)
              .replace('%3', colorType)
              .replace('%4', paletteName)
              .replace('%5', VALID_HUE_VALUES.join(', '))
            );
          }
        });
        return self;
      };

      self[colorType + 'Color'] = function() {
        var args = Array.prototype.slice.call(arguments);
        console.warn('$mdThemingProviderTheme.' + colorType + 'Color() has been deprecated. ' +
                     'Use $mdThemingProviderTheme.' + colorType + 'Palette() instead.');
        return self[colorType + 'Palette'].apply(self, args);
      };
    });
  }

  /**
   * @ngdoc service
   * @name $mdTheming
   * @module material.core.theming
   *
   * @description
   *
   * Service that makes an element apply theming related <b>classes</b> to itself.
   *
   * <hljs lang="js">
   * app.directive('myFancyDirective', function($mdTheming) {
   *   return {
   *     restrict: 'e',
   *     link: function(scope, el, attrs) {
   *       $mdTheming(el);
   *
   *       $mdTheming.defineTheme('myTheme', {
   *         primary: 'blue',
   *         accent: 'pink',
   *         dark: true
   *       })
   *     }
   *   };
   * });
   * </hljs>
   * @param {element=} element to apply theming to
   */

  /**
   * @ngdoc property
   * @name $mdTheming#THEMES
   * @description
   * Property to get all the themes defined
   * @returns {Object} All the themes defined with their properties
   */

  /**
   * @ngdoc property
   * @name $mdTheming#PALETTES
   * @description
   * Property to get all the palettes defined
   * @returns {Object} All the palettes defined with their colors
   */

  /**
   * @ngdoc method
   * @name $mdTheming#registered
   * @description
   * Determine is specified theme name is a valid, registered theme
   * @param {string} themeName the theme to check if registered
   * @returns {boolean} whether the theme is registered or not
   */

  /**
   * @ngdoc method
   * @name $mdTheming#defaultTheme
   * @description
   * Returns the default theme
   * @returns {string} The default theme
   */

  /**
   * @ngdoc method
   * @name $mdTheming#generateTheme
   * @description
   * Lazy generate themes - by default, every theme is generated when defined.
   * You can disable this in the configuration section using the
   * `$mdThemingProvider.generateThemesOnDemand(true);`
   *
   * The theme name that is passed in must match the name of the theme that was defined as part of the configuration block.
   *
   * @param name {string} theme name to generate
   */

  /**
   * @ngdoc method
   * @name $mdTheming#setBrowserColor
   * @description
   * Sets browser header coloring
   * for more info please visit:
   * https://developers.google.com/web/fundamentals/design-and-ui/browser-customization/theme-color
   *
   * The default color is `800` from `primary` palette of the `default` theme
   *
   * options are:<br/>
   * `theme`   - A defined theme via `$mdThemeProvider` to use the palettes from. Default is `default` theme.<br/>
   * `palette` - Can be any one of the basic material design palettes, extended defined palettes and 'primary',
   *             'accent', 'background' and 'warn'. Default is `primary`<br/>
   * `hue`     - The hue from the selected palette. Default is `800`
   *
   * @param {Object} options Options object for the browser color
   * @returns {Function} remove function of the browser color
   */

  /**
   * @ngdoc method
   * @name $mdTheming#defineTheme
   * @description
   * Dynamically define a theme by an options object
   *
   * options are:<br/>
   * `primary`    - The primary palette of the theme.<br/>
   * `accent`     - The accent palette of the theme.<br/>
   * `warn`       - The warn palette of the theme.<br/>
   * `background` - The background palette of the theme.<br/>
   * `dark`       - Indicates if it's a dark theme.<br/>
   *
   * @param {String} name Theme name to define
   * @param {Object} options Theme definition options
   * @returns {Promise<string>} A resolved promise with the theme name
   */

  /* @ngInject */
  function ThemingService($rootScope, $mdUtil, $q, $log) {
        // Allow us to be invoked via a linking function signature.
    var applyTheme = function (scope, el) {
          if (el === undefined) { el = scope; scope = undefined; }
          if (scope === undefined) { scope = $rootScope; }
          applyTheme.inherit(el, el);
        };

    Object.defineProperty(applyTheme, 'THEMES', {
      get: function () {
        return angular.extend({}, THEMES);
      }
    });
    Object.defineProperty(applyTheme, 'PALETTES', {
      get: function () {
        return angular.extend({}, PALETTES);
      }
    });
    Object.defineProperty(applyTheme, 'ALWAYS_WATCH', {
      get: function () {
        return alwaysWatchTheme;
      }
    });
    applyTheme.inherit = inheritTheme;
    applyTheme.registered = registered;
    applyTheme.defaultTheme = function() { return defaultTheme; };
    applyTheme.generateTheme = function(name) { generateTheme(THEMES[name], name, themeConfig.nonce); };
    applyTheme.defineTheme = function(name, options) {
      options = options || {};

      var theme = registerTheme(name);

      if (options.primary) {
        theme.primaryPalette(options.primary);
      }
      if (options.accent) {
        theme.accentPalette(options.accent);
      }
      if (options.warn) {
        theme.warnPalette(options.warn);
      }
      if (options.background) {
        theme.backgroundPalette(options.background);
      }
      if (options.dark){
        theme.dark();
      }

      this.generateTheme(name);

      return $q.resolve(name);
    };
    applyTheme.setBrowserColor = enableBrowserColor;

    return applyTheme;

    /**
     * Determine is specified theme name is a valid, registered theme
     */
    function registered(themeName) {
      if (themeName === undefined || themeName === '') return true;
      return applyTheme.THEMES[themeName] !== undefined;
    }

    /**
     * Get theme name for the element, then update with Theme CSS class
     */
    function inheritTheme (el, parent) {
      var ctrl = parent.controller('mdTheme') || el.data('$mdThemeController');

      updateThemeClass(lookupThemeName());

      if (ctrl) {
        var watchTheme = alwaysWatchTheme ||
                         ctrl.$shouldWatch ||
                         $mdUtil.parseAttributeBoolean(el.attr('md-theme-watch'));

        var unwatch = ctrl.registerChanges(function (name) {
          updateThemeClass(name);

          if (!watchTheme) {
            unwatch();
          }
          else {
            el.on('$destroy', unwatch);
          }
        });
      }

      /**
       * Find the theme name from the parent controller or element data
       */
      function lookupThemeName() {
        // As a few components (dialog) add their controllers later, we should also watch for a controller init.
        return ctrl && ctrl.$mdTheme || (defaultTheme == 'default' ? '' : defaultTheme);
      }

      /**
       * Remove old theme class and apply a new one
       * NOTE: if not a valid theme name, then the current name is not changed
       */
      function updateThemeClass(theme) {
        if (!theme) return;
        if (!registered(theme)) {
          $log.warn('Attempted to use unregistered theme \'' + theme + '\'. ' +
                    'Register it with $mdThemingProvider.theme().');
        }

        var oldTheme = el.data('$mdThemeName');
        if (oldTheme) el.removeClass('md-' + oldTheme +'-theme');
        el.addClass('md-' + theme + '-theme');
        el.data('$mdThemeName', theme);
        if (ctrl) {
          el.data('$mdThemeController', ctrl);
        }
      }
    }

  }
}

function ThemingDirective($mdTheming, $interpolate, $parse, $mdUtil, $q, $log) {
  return {
    priority: 101, // has to be more than 100 to be before interpolation (issue on IE)
    link: {
      pre: function(scope, el, attrs) {
        var registeredCallbacks = [];

        var startSymbol = $interpolate.startSymbol();
        var endSymbol = $interpolate.endSymbol();

        var theme = attrs.mdTheme.trim();

        var hasInterpolation =
          theme.substr(0, startSymbol.length) === startSymbol &&
          theme.lastIndexOf(endSymbol) === theme.length - endSymbol.length;

        var oneTimeOperator = '::';
        var oneTimeBind = attrs.mdTheme
            .split(startSymbol).join('')
            .split(endSymbol).join('')
            .trim()
            .substr(0, oneTimeOperator.length) === oneTimeOperator;

        var ctrl = {
          registerChanges: function (cb, context) {
            if (context) {
              cb = angular.bind(context, cb);
            }

            registeredCallbacks.push(cb);

            return function () {
              var index = registeredCallbacks.indexOf(cb);

              if (index > -1) {
                registeredCallbacks.splice(index, 1);
              }
            };
          },
          $setTheme: function (theme) {
            if (!$mdTheming.registered(theme)) {
              $log.warn('attempted to use unregistered theme \'' + theme + '\'');
            }

            ctrl.$mdTheme = theme;

            // Iterating backwards to support unregistering during iteration
            // http://stackoverflow.com/a/9882349/890293
            // we don't use `reverse()` of array because it mutates the array and we don't want it to get re-indexed
            for (var i = registeredCallbacks.length; i--;) {
              registeredCallbacks[i](theme);
            }
          },
          $shouldWatch: $mdUtil.parseAttributeBoolean(el.attr('md-theme-watch')) ||
                        $mdTheming.ALWAYS_WATCH ||
                        (hasInterpolation && !oneTimeBind)
        };

        el.data('$mdThemeController', ctrl);

        var getTheme = function () {
          var interpolation = $interpolate(attrs.mdTheme)(scope);
          return $parse(interpolation)(scope) || interpolation;
        };

        var setParsedTheme = function (theme) {
          if (typeof theme === 'string') {
            return ctrl.$setTheme(theme);
          }

          $q.when( angular.isFunction(theme) ?  theme() : theme )
            .then(function(name){
              ctrl.$setTheme(name);
            });
        };

        setParsedTheme(getTheme());

        var unwatch = scope.$watch(getTheme, function(theme) {
          if (theme) {
            setParsedTheme(theme);

            if (!ctrl.$shouldWatch) {
              unwatch();
            }
          }
        });
      }
    }
  };
}

/**
 * Special directive that will disable ALL runtime Theme style generation and DOM injection
 *
 * <link rel="stylesheet" href="angular-material.min.css">
 * <link rel="stylesheet" href="angular-material.themes.css">
 *
 * <body md-themes-disabled>
 *  ...
 * </body>
 *
 * Note: Using md-themes-css directive requires the developer to load external
 * theme stylesheets; e.g. custom themes from Material-Tools:
 *
 *       `angular-material.themes.css`
 *
 * Another option is to use the ThemingProvider to configure and disable the attribute
 * conversions; this would obviate the use of the `md-themes-css` directive
 *
 */
function disableThemesDirective() {
  themeConfig.disableTheming = true;

  // Return a 1x-only, first-match attribute directive
  return {
    restrict : 'A',
    priority : '900'
  };
}

function ThemableDirective($mdTheming) {
  return $mdTheming;
}

function parseRules(theme, colorType, rules) {
  checkValidPalette(theme, colorType);

  rules = rules.replace(/THEME_NAME/g, theme.name);
  var generatedRules = [];
  var color = theme.colors[colorType];

  var themeNameRegex = new RegExp('\\.md-' + theme.name + '-theme', 'g');
  // Matches '{{ primary-color }}', etc
  var hueRegex = new RegExp('(\'|")?{{\\s*(' + colorType + ')-(color|contrast)-?(\\d\\.?\\d*)?\\s*}}(\"|\')?','g');
  var simpleVariableRegex = /'?"?\{\{\s*([a-zA-Z]+)-(A?\d+|hue\-[0-3]|shadow|default)-?(\d\.?\d*)?(contrast)?\s*\}\}'?"?/g;
  var palette = PALETTES[color.name];

  // find and replace simple variables where we use a specific hue, not an entire palette
  // eg. "{{primary-100}}"
  //\(' + THEME_COLOR_TYPES.join('\|') + '\)'
  rules = rules.replace(simpleVariableRegex, function(match, colorType, hue, opacity, contrast) {
    if (colorType === 'foreground') {
      if (hue == 'shadow') {
        return theme.foregroundShadow;
      } else {
        return theme.foregroundPalette[hue] || theme.foregroundPalette['1'];
      }
    }

    // `default` is also accepted as a hue-value, because the background palettes are
    // using it as a name for the default hue.
    if (hue.indexOf('hue') === 0 || hue === 'default') {
      hue = theme.colors[colorType].hues[hue];
    }

    return rgba( (PALETTES[ theme.colors[colorType].name ][hue] || '')[contrast ? 'contrast' : 'value'], opacity );
  });

  // For each type, generate rules for each hue (ie. default, md-hue-1, md-hue-2, md-hue-3)
  angular.forEach(color.hues, function(hueValue, hueName) {
    var newRule = rules
      .replace(hueRegex, function(match, _, colorType, hueType, opacity) {
        return rgba(palette[hueValue][hueType === 'color' ? 'value' : 'contrast'], opacity);
      });
    if (hueName !== 'default') {
      newRule = newRule.replace(themeNameRegex, '.md-' + theme.name + '-theme.md-' + hueName);
    }

    // Don't apply a selector rule to the default theme, making it easier to override
    // styles of the base-component
    if (theme.name == 'default') {
      var themeRuleRegex = /((?:\s|>|\.|\w|-|:|\(|\)|\[|\]|"|'|=)*)\.md-default-theme((?:\s|>|\.|\w|-|:|\(|\)|\[|\]|"|'|=)*)/g;

      newRule = newRule.replace(themeRuleRegex, function(match, start, end) {
        return match + ', ' + start + end;
      });
    }
    generatedRules.push(newRule);
  });

  return generatedRules;
}

var rulesByType = {};

// Generate our themes at run time given the state of THEMES and PALETTES
function generateAllThemes($injector, $mdTheming) {
  var head = document.head;
  var firstChild = head ? head.firstElementChild : null;
  var themeCss = !themeConfig.disableTheming && $injector.has('$MD_THEME_CSS') ? $injector.get('$MD_THEME_CSS') : '';

  // Append our custom registered styles to the theme stylesheet.
  themeCss += themeConfig.registeredStyles.join('');

  if ( !firstChild ) return;
  if (themeCss.length === 0) return; // no rules, so no point in running this expensive task

  // Expose contrast colors for palettes to ensure that text is always readable
  angular.forEach(PALETTES, sanitizePalette);

  // MD_THEME_CSS is a string generated by the build process that includes all the themable
  // components as templates

  // Break the CSS into individual rules
  var rules = themeCss
                  .split(/\}(?!(\}|'|"|;))/)
                  .filter(function(rule) { return rule && rule.trim().length; })
                  .map(function(rule) { return rule.trim() + '}'; });


  var ruleMatchRegex = new RegExp('md-(' + THEME_COLOR_TYPES.join('|') + ')', 'g');

  THEME_COLOR_TYPES.forEach(function(type) {
    rulesByType[type] = '';
  });


  // Sort the rules based on type, allowing us to do color substitution on a per-type basis
  rules.forEach(function(rule) {
    var match = rule.match(ruleMatchRegex);
    // First: test that if the rule has '.md-accent', it goes into the accent set of rules
    for (var i = 0, type; type = THEME_COLOR_TYPES[i]; i++) {
      if (rule.indexOf('.md-' + type) > -1) {
        return rulesByType[type] += rule;
      }
    }

    // If no eg 'md-accent' class is found, try to just find 'accent' in the rule and guess from
    // there
    for (i = 0; type = THEME_COLOR_TYPES[i]; i++) {
      if (rule.indexOf(type) > -1) {
        return rulesByType[type] += rule;
      }
    }

    // Default to the primary array
    return rulesByType[DEFAULT_COLOR_TYPE] += rule;
  });

  // If themes are being generated on-demand, quit here. The user will later manually
  // call generateTheme to do this on a theme-by-theme basis.
  if (themeConfig.generateOnDemand) return;

  angular.forEach($mdTheming.THEMES, function(theme) {
    if (!GENERATED[theme.name] && !($mdTheming.defaultTheme() !== 'default' && theme.name === 'default')) {
      generateTheme(theme, theme.name, themeConfig.nonce);
    }
  });


  // *************************
  // Internal functions
  // *************************

  // The user specifies a 'default' contrast color as either light or dark,
  // then explicitly lists which hues are the opposite contrast (eg. A100 has dark, A200 has light)
  function sanitizePalette(palette, name) {
    var defaultContrast = palette.contrastDefaultColor;
    var lightColors = palette.contrastLightColors || [];
    var strongLightColors = palette.contrastStrongLightColors || [];
    var darkColors = palette.contrastDarkColors || [];

    // These colors are provided as space-separated lists
    if (typeof lightColors === 'string') lightColors = lightColors.split(' ');
    if (typeof strongLightColors === 'string') strongLightColors = strongLightColors.split(' ');
    if (typeof darkColors === 'string') darkColors = darkColors.split(' ');

    // Cleanup after ourselves
    delete palette.contrastDefaultColor;
    delete palette.contrastLightColors;
    delete palette.contrastStrongLightColors;
    delete palette.contrastDarkColors;

    // Change { 'A100': '#fffeee' } to { 'A100': { value: '#fffeee', contrast:DARK_CONTRAST_COLOR }
    angular.forEach(palette, function(hueValue, hueName) {
      if (angular.isObject(hueValue)) return; // Already converted
      // Map everything to rgb colors
      var rgbValue = colorToRgbaArray(hueValue);
      if (!rgbValue) {
        throw new Error("Color %1, in palette %2's hue %3, is invalid. Hex or rgb(a) color expected."
                        .replace('%1', hueValue)
                        .replace('%2', palette.name)
                        .replace('%3', hueName));
      }

      palette[hueName] = {
        hex: palette[hueName],
        value: rgbValue,
        contrast: getContrastColor()
      };
      function getContrastColor() {
        if (defaultContrast === 'light') {
          if (darkColors.indexOf(hueName) > -1) {
            return DARK_CONTRAST_COLOR;
          } else {
            return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
              : LIGHT_CONTRAST_COLOR;
          }
        } else {
          if (lightColors.indexOf(hueName) > -1) {
            return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
              : LIGHT_CONTRAST_COLOR;
          } else {
            return DARK_CONTRAST_COLOR;
          }
        }
      }
    });
  }
}

function generateTheme(theme, name, nonce) {
  var head = document.head;
  var firstChild = head ? head.firstElementChild : null;

  if (!GENERATED[name]) {
    // For each theme, use the color palettes specified for
    // `primary`, `warn` and `accent` to generate CSS rules.
    THEME_COLOR_TYPES.forEach(function(colorType) {
      var styleStrings = parseRules(theme, colorType, rulesByType[colorType]);
      while (styleStrings.length) {
        var styleContent = styleStrings.shift();
        if (styleContent) {
          var style = document.createElement('style');
          style.setAttribute('md-theme-style', '');
          if (nonce) {
            style.setAttribute('nonce', nonce);
          }
          style.appendChild(document.createTextNode(styleContent));
          head.insertBefore(style, firstChild);
        }
      }
    });

    GENERATED[theme.name] = true;
  }

}


function checkValidPalette(theme, colorType) {
  // If theme attempts to use a palette that doesnt exist, throw error
  if (!PALETTES[ (theme.colors[colorType] || {}).name ]) {
    throw new Error(
      "You supplied an invalid color palette for theme %1's %2 palette. Available palettes: %3"
                    .replace('%1', theme.name)
                    .replace('%2', colorType)
                    .replace('%3', Object.keys(PALETTES).join(', '))
    );
  }
}

function colorToRgbaArray(clr) {
  if (angular.isArray(clr) && clr.length == 3) return clr;
  if (/^rgb/.test(clr)) {
    return clr.replace(/(^\s*rgba?\(|\)\s*$)/g, '').split(',').map(function(value, i) {
      return i == 3 ? parseFloat(value, 10) : parseInt(value, 10);
    });
  }
  if (clr.charAt(0) == '#') clr = clr.substring(1);
  if (!/^([a-fA-F0-9]{3}){1,2}$/g.test(clr)) return;

  var dig = clr.length / 3;
  var red = clr.substr(0, dig);
  var grn = clr.substr(dig, dig);
  var blu = clr.substr(dig * 2);
  if (dig === 1) {
    red += red;
    grn += grn;
    blu += blu;
  }
  return [parseInt(red, 16), parseInt(grn, 16), parseInt(blu, 16)];
}

function rgba(rgbArray, opacity) {
  if ( !rgbArray ) return "rgb('0,0,0')";

  if (rgbArray.length == 4) {
    rgbArray = angular.copy(rgbArray);
    opacity ? rgbArray.pop() : opacity = rgbArray.pop();
  }
  return opacity && (typeof opacity == 'number' || (typeof opacity == 'string' && opacity.length)) ?
    'rgba(' + rgbArray.join(',') + ',' + opacity + ')' :
    'rgb(' + rgbArray.join(',') + ')';
}


})(window.angular);

})();
(function(){
"use strict";

// Polyfill angular < 1.4 (provide $animateCss)
angular
  .module('material.core')
  .factory('$$mdAnimate', ["$q", "$timeout", "$mdConstant", "$animateCss", function($q, $timeout, $mdConstant, $animateCss){

     // Since $$mdAnimate is injected into $mdUtil... use a wrapper function
     // to subsequently inject $mdUtil as an argument to the AnimateDomUtils

     return function($mdUtil) {
       return AnimateDomUtils( $mdUtil, $q, $timeout, $mdConstant, $animateCss);
     };
   }]);

/**
 * Factory function that requires special injections
 */
function AnimateDomUtils($mdUtil, $q, $timeout, $mdConstant, $animateCss) {
  var self;
  return self = {
    /**
     *
     */
    translate3d : function( target, from, to, options ) {
      return $animateCss(target, {
        from: from,
        to: to,
        addClass: options.transitionInClass,
        removeClass: options.transitionOutClass,
        duration: options.duration
      })
      .start()
      .then(function(){
          // Resolve with reverser function...
          return reverseTranslate;
      });

      /**
       * Specific reversal of the request translate animation above...
       */
      function reverseTranslate (newFrom) {
        return $animateCss(target, {
           to: newFrom || from,
           addClass: options.transitionOutClass,
           removeClass: options.transitionInClass,
           duration: options.duration
        }).start();

      }
    },

    /**
     * Listen for transitionEnd event (with optional timeout)
     * Announce completion or failure via promise handlers
     */
    waitTransitionEnd: function (element, opts) {
      var TIMEOUT = 3000; // fallback is 3 secs

      return $q(function(resolve, reject){
        opts = opts || { };

        // If there is no transition is found, resolve immediately
        //
        // NOTE: using $mdUtil.nextTick() causes delays/issues
        if (noTransitionFound(opts.cachedTransitionStyles)) {
          TIMEOUT = 0;
        }

        var timer = $timeout(finished, opts.timeout || TIMEOUT);
        element.on($mdConstant.CSS.TRANSITIONEND, finished);

        /**
         * Upon timeout or transitionEnd, reject or resolve (respectively) this promise.
         * NOTE: Make sure this transitionEnd didn't bubble up from a child
         */
        function finished(ev) {
          if ( ev && ev.target !== element[0]) return;

          if ( ev  ) $timeout.cancel(timer);
          element.off($mdConstant.CSS.TRANSITIONEND, finished);

          // Never reject since ngAnimate may cause timeouts due missed transitionEnd events
          resolve();

        }

        /**
         * Checks whether or not there is a transition.
         *
         * @param styles The cached styles to use for the calculation. If null, getComputedStyle()
         * will be used.
         *
         * @returns {boolean} True if there is no transition/duration; false otherwise.
         */
        function noTransitionFound(styles) {
          styles = styles || window.getComputedStyle(element[0]);

          return styles.transitionDuration == '0s' || (!styles.transition && !styles.transitionProperty);
        }

      });
    },

    calculateTransformValues: function (element, originator) {
      var origin = originator.element;
      var bounds = originator.bounds;

      if (origin || bounds) {
        var originBnds = origin ? self.clientRect(origin) || currentBounds() : self.copyRect(bounds);
        var dialogRect = self.copyRect(element[0].getBoundingClientRect());
        var dialogCenterPt = self.centerPointFor(dialogRect);
        var originCenterPt = self.centerPointFor(originBnds);

        return {
          centerX: originCenterPt.x - dialogCenterPt.x,
          centerY: originCenterPt.y - dialogCenterPt.y,
          scaleX: Math.round(100 * Math.min(0.5, originBnds.width / dialogRect.width)) / 100,
          scaleY: Math.round(100 * Math.min(0.5, originBnds.height / dialogRect.height)) / 100
        };
      }
      return {centerX: 0, centerY: 0, scaleX: 0.5, scaleY: 0.5};

      /**
       * This is a fallback if the origin information is no longer valid, then the
       * origin bounds simply becomes the current bounds for the dialogContainer's parent
       */
      function currentBounds() {
        var cntr = element ? element.parent() : null;
        var parent = cntr ? cntr.parent() : null;

        return parent ? self.clientRect(parent) : null;
      }
    },

    /**
     * Calculate the zoom transform from dialog to origin.
     *
     * We use this to set the dialog position immediately;
     * then the md-transition-in actually translates back to
     * `translate3d(0,0,0) scale(1.0)`...
     *
     * NOTE: all values are rounded to the nearest integer
     */
    calculateZoomToOrigin: function (element, originator) {
      var zoomTemplate = "translate3d( {centerX}px, {centerY}px, 0 ) scale( {scaleX}, {scaleY} )";
      var buildZoom = angular.bind(null, $mdUtil.supplant, zoomTemplate);

      return buildZoom(self.calculateTransformValues(element, originator));
    },

    /**
     * Calculate the slide transform from panel to origin.
     * NOTE: all values are rounded to the nearest integer
     */
    calculateSlideToOrigin: function (element, originator) {
      var slideTemplate = "translate3d( {centerX}px, {centerY}px, 0 )";
      var buildSlide = angular.bind(null, $mdUtil.supplant, slideTemplate);

      return buildSlide(self.calculateTransformValues(element, originator));
    },

    /**
     * Enhance raw values to represent valid css stylings...
     */
    toCss : function( raw ) {
      var css = { };
      var lookups = 'left top right bottom width height x y min-width min-height max-width max-height';

      angular.forEach(raw, function(value,key) {
        if ( angular.isUndefined(value) ) return;

        if ( lookups.indexOf(key) >= 0 ) {
          css[key] = value + 'px';
        } else {
          switch (key) {
            case 'transition':
              convertToVendor(key, $mdConstant.CSS.TRANSITION, value);
              break;
            case 'transform':
              convertToVendor(key, $mdConstant.CSS.TRANSFORM, value);
              break;
            case 'transformOrigin':
              convertToVendor(key, $mdConstant.CSS.TRANSFORM_ORIGIN, value);
              break;
            case 'font-size':
              css['font-size'] = value; // font sizes aren't always in px
              break;
          }
        }
      });

      return css;

      function convertToVendor(key, vendor, value) {
        angular.forEach(vendor.split(' '), function (key) {
          css[key] = value;
        });
      }
    },

    /**
     * Convert the translate CSS value to key/value pair(s).
     */
    toTransformCss: function (transform, addTransition, transition) {
      var css = {};
      angular.forEach($mdConstant.CSS.TRANSFORM.split(' '), function (key) {
        css[key] = transform;
      });

      if (addTransition) {
        transition = transition || "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important";
        css.transition = transition;
      }

      return css;
    },

    /**
     *  Clone the Rect and calculate the height/width if needed
     */
    copyRect: function (source, destination) {
      if (!source) return null;

      destination = destination || {};

      angular.forEach('left top right bottom width height'.split(' '), function (key) {
        destination[key] = Math.round(source[key]);
      });

      destination.width = destination.width || (destination.right - destination.left);
      destination.height = destination.height || (destination.bottom - destination.top);

      return destination;
    },

    /**
     * Calculate ClientRect of element; return null if hidden or zero size
     */
    clientRect: function (element) {
      var bounds = angular.element(element)[0].getBoundingClientRect();
      var isPositiveSizeClientRect = function (rect) {
        return rect && (rect.width > 0) && (rect.height > 0);
      };

      // If the event origin element has zero size, it has probably been hidden.
      return isPositiveSizeClientRect(bounds) ? self.copyRect(bounds) : null;
    },

    /**
     *  Calculate 'rounded' center point of Rect
     */
    centerPointFor: function (targetRect) {
      return targetRect ? {
        x: Math.round(targetRect.left + (targetRect.width / 2)),
        y: Math.round(targetRect.top + (targetRect.height / 2))
      } : { x : 0, y : 0 };
    }

  };
}


})();
(function(){
"use strict";

if (angular.version.minor >= 4) {
  angular.module('material.core.animate', []);
} else {
(function() {
  "use strict";

  var forEach = angular.forEach;

  var WEBKIT = angular.isDefined(document.documentElement.style.WebkitAppearance);
  var TRANSITION_PROP = WEBKIT ? 'WebkitTransition' : 'transition';
  var ANIMATION_PROP = WEBKIT ? 'WebkitAnimation' : 'animation';
  var PREFIX = WEBKIT ? '-webkit-' : '';

  var TRANSITION_EVENTS = (WEBKIT ? 'webkitTransitionEnd ' : '') + 'transitionend';
  var ANIMATION_EVENTS = (WEBKIT ? 'webkitAnimationEnd ' : '') + 'animationend';

  var $$ForceReflowFactory = ['$document', function($document) {
    return function() {
      return $document[0].body.clientWidth + 1;
    };
  }];

  var $$rAFMutexFactory = ['$$rAF', function($$rAF) {
    return function() {
      var passed = false;
      $$rAF(function() {
        passed = true;
      });
      return function(fn) {
        passed ? fn() : $$rAF(fn);
      };
    };
  }];

  var $$AnimateRunnerFactory = ['$q', '$$rAFMutex', function($q, $$rAFMutex) {
    var INITIAL_STATE = 0;
    var DONE_PENDING_STATE = 1;
    var DONE_COMPLETE_STATE = 2;

    function AnimateRunner(host) {
      this.setHost(host);

      this._doneCallbacks = [];
      this._runInAnimationFrame = $$rAFMutex();
      this._state = 0;
    }

    AnimateRunner.prototype = {
      setHost: function(host) {
        this.host = host || {};
      },

      done: function(fn) {
        if (this._state === DONE_COMPLETE_STATE) {
          fn();
        } else {
          this._doneCallbacks.push(fn);
        }
      },

      progress: angular.noop,

      getPromise: function() {
        if (!this.promise) {
          var self = this;
          this.promise = $q(function(resolve, reject) {
            self.done(function(status) {
              status === false ? reject() : resolve();
            });
          });
        }
        return this.promise;
      },

      then: function(resolveHandler, rejectHandler) {
        return this.getPromise().then(resolveHandler, rejectHandler);
      },

      'catch': function(handler) {
        return this.getPromise()['catch'](handler);
      },

      'finally': function(handler) {
        return this.getPromise()['finally'](handler);
      },

      pause: function() {
        if (this.host.pause) {
          this.host.pause();
        }
      },

      resume: function() {
        if (this.host.resume) {
          this.host.resume();
        }
      },

      end: function() {
        if (this.host.end) {
          this.host.end();
        }
        this._resolve(true);
      },

      cancel: function() {
        if (this.host.cancel) {
          this.host.cancel();
        }
        this._resolve(false);
      },

      complete: function(response) {
        var self = this;
        if (self._state === INITIAL_STATE) {
          self._state = DONE_PENDING_STATE;
          self._runInAnimationFrame(function() {
            self._resolve(response);
          });
        }
      },

      _resolve: function(response) {
        if (this._state !== DONE_COMPLETE_STATE) {
          forEach(this._doneCallbacks, function(fn) {
            fn(response);
          });
          this._doneCallbacks.length = 0;
          this._state = DONE_COMPLETE_STATE;
        }
      }
    };

    // Polyfill AnimateRunner.all which is used by input animations
    AnimateRunner.all = function(runners, callback) {
      var count = 0;
      var status = true;
      forEach(runners, function(runner) {
        runner.done(onProgress);
      });

      function onProgress(response) {
        status = status && response;
        if (++count === runners.length) {
          callback(status);
        }
      }
    };

    return AnimateRunner;
  }];

  angular
    .module('material.core.animate', [])
    .factory('$$forceReflow', $$ForceReflowFactory)
    .factory('$$AnimateRunner', $$AnimateRunnerFactory)
    .factory('$$rAFMutex', $$rAFMutexFactory)
    .factory('$animateCss', ['$window', '$$rAF', '$$AnimateRunner', '$$forceReflow', '$$jqLite', '$timeout', '$animate',
                     function($window,   $$rAF,   $$AnimateRunner,   $$forceReflow,   $$jqLite,   $timeout, $animate) {

      function init(element, options) {

        var temporaryStyles = [];
        var node = getDomNode(element);
        var areAnimationsAllowed = node && $animate.enabled();

        var hasCompleteStyles = false;
        var hasCompleteClasses = false;

        if (areAnimationsAllowed) {
          if (options.transitionStyle) {
            temporaryStyles.push([PREFIX + 'transition', options.transitionStyle]);
          }

          if (options.keyframeStyle) {
            temporaryStyles.push([PREFIX + 'animation', options.keyframeStyle]);
          }

          if (options.delay) {
            temporaryStyles.push([PREFIX + 'transition-delay', options.delay + 's']);
          }

          if (options.duration) {
            temporaryStyles.push([PREFIX + 'transition-duration', options.duration + 's']);
          }

          hasCompleteStyles = options.keyframeStyle ||
              (options.to && (options.duration > 0 || options.transitionStyle));
          hasCompleteClasses = !!options.addClass || !!options.removeClass;

          blockTransition(element, true);
        }

        var hasCompleteAnimation = areAnimationsAllowed && (hasCompleteStyles || hasCompleteClasses);

        applyAnimationFromStyles(element, options);

        var animationClosed = false;
        var events, eventFn;

        return {
          close: $window.close,
          start: function() {
            var runner = new $$AnimateRunner();
            waitUntilQuiet(function() {
              blockTransition(element, false);
              if (!hasCompleteAnimation) {
                return close();
              }

              forEach(temporaryStyles, function(entry) {
                var key = entry[0];
                var value = entry[1];
                node.style[camelCase(key)] = value;
              });

              applyClasses(element, options);

              var timings = computeTimings(element);
              if (timings.duration === 0) {
                return close();
              }

              var moreStyles = [];

              if (options.easing) {
                if (timings.transitionDuration) {
                  moreStyles.push([PREFIX + 'transition-timing-function', options.easing]);
                }
                if (timings.animationDuration) {
                  moreStyles.push([PREFIX + 'animation-timing-function', options.easing]);
                }
              }

              if (options.delay && timings.animationDelay) {
                moreStyles.push([PREFIX + 'animation-delay', options.delay + 's']);
              }

              if (options.duration && timings.animationDuration) {
                moreStyles.push([PREFIX + 'animation-duration', options.duration + 's']);
              }

              forEach(moreStyles, function(entry) {
                var key = entry[0];
                var value = entry[1];
                node.style[camelCase(key)] = value;
                temporaryStyles.push(entry);
              });

              var maxDelay = timings.delay;
              var maxDelayTime = maxDelay * 1000;
              var maxDuration = timings.duration;
              var maxDurationTime = maxDuration * 1000;
              var startTime = Date.now();

              events = [];
              if (timings.transitionDuration) {
                events.push(TRANSITION_EVENTS);
              }
              if (timings.animationDuration) {
                events.push(ANIMATION_EVENTS);
              }
              events = events.join(' ');
              eventFn = function(event) {
                event.stopPropagation();
                var ev = event.originalEvent || event;
                var timeStamp = ev.timeStamp || Date.now();
                var elapsedTime = parseFloat(ev.elapsedTime.toFixed(3));
                if (Math.max(timeStamp - startTime, 0) >= maxDelayTime && elapsedTime >= maxDuration) {
                  close();
                }
              };
              element.on(events, eventFn);

              applyAnimationToStyles(element, options);

              $timeout(close, maxDelayTime + maxDurationTime * 1.5, false);
            });

            return runner;

            function close() {
              if (animationClosed) return;
              animationClosed = true;

              if (events && eventFn) {
                element.off(events, eventFn);
              }
              applyClasses(element, options);
              applyAnimationStyles(element, options);
              forEach(temporaryStyles, function(entry) {
                node.style[camelCase(entry[0])] = '';
              });
              runner.complete(true);
              return runner;
            }
          }
        };
      }

      function applyClasses(element, options) {
        if (options.addClass) {
          $$jqLite.addClass(element, options.addClass);
          options.addClass = null;
        }
        if (options.removeClass) {
          $$jqLite.removeClass(element, options.removeClass);
          options.removeClass = null;
        }
      }

      function computeTimings(element) {
        var node = getDomNode(element);
        var cs = $window.getComputedStyle(node);
        var tdr = parseMaxTime(cs[prop('transitionDuration')]);
        var adr = parseMaxTime(cs[prop('animationDuration')]);
        var tdy = parseMaxTime(cs[prop('transitionDelay')]);
        var ady = parseMaxTime(cs[prop('animationDelay')]);

        adr *= (parseInt(cs[prop('animationIterationCount')], 10) || 1);
        var duration = Math.max(adr, tdr);
        var delay = Math.max(ady, tdy);

        return {
          duration: duration,
          delay: delay,
          animationDuration: adr,
          transitionDuration: tdr,
          animationDelay: ady,
          transitionDelay: tdy
        };

        function prop(key) {
          return WEBKIT ? 'Webkit' + key.charAt(0).toUpperCase() + key.substr(1)
                        : key;
        }
      }

      function parseMaxTime(str) {
        var maxValue = 0;
        var values = (str || "").split(/\s*,\s*/);
        forEach(values, function(value) {
          // it's always safe to consider only second values and omit `ms` values since
          // getComputedStyle will always handle the conversion for us
          if (value.charAt(value.length - 1) == 's') {
            value = value.substring(0, value.length - 1);
          }
          value = parseFloat(value) || 0;
          maxValue = maxValue ? Math.max(value, maxValue) : value;
        });
        return maxValue;
      }

      var cancelLastRAFRequest;
      var rafWaitQueue = [];
      function waitUntilQuiet(callback) {
        if (cancelLastRAFRequest) {
          cancelLastRAFRequest(); //cancels the request
        }
        rafWaitQueue.push(callback);
        cancelLastRAFRequest = $$rAF(function() {
          cancelLastRAFRequest = null;

          // DO NOT REMOVE THIS LINE OR REFACTOR OUT THE `pageWidth` variable.
          // PLEASE EXAMINE THE `$$forceReflow` service to understand why.
          var pageWidth = $$forceReflow();

          // we use a for loop to ensure that if the queue is changed
          // during this looping then it will consider new requests
          for (var i = 0; i < rafWaitQueue.length; i++) {
            rafWaitQueue[i](pageWidth);
          }
          rafWaitQueue.length = 0;
        });
      }

      function applyAnimationStyles(element, options) {
        applyAnimationFromStyles(element, options);
        applyAnimationToStyles(element, options);
      }

      function applyAnimationFromStyles(element, options) {
        if (options.from) {
          element.css(options.from);
          options.from = null;
        }
      }

      function applyAnimationToStyles(element, options) {
        if (options.to) {
          element.css(options.to);
          options.to = null;
        }
      }

      function getDomNode(element) {
        for (var i = 0; i < element.length; i++) {
          if (element[i].nodeType === 1) return element[i];
        }
      }

      function blockTransition(element, bool) {
        var node = getDomNode(element);
        var key = camelCase(PREFIX + 'transition-delay');
        node.style[key] = bool ? '-9999s' : '';
      }

      return init;
    }]);

  /**
   * Older browsers [FF31] expect camelCase
   * property keys.
   * e.g.
   *  animation-duration --> animationDuration
   */
  function camelCase(str) {
    return str.replace(/-[a-z]/g, function(str) {
      return str.charAt(1).toUpperCase();
    });
  }

})();

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.autocomplete
 */
/*
 * @see js folder for autocomplete implementation
 */
angular.module('material.components.autocomplete', [
  'material.core',
  'material.components.icon',
  'material.components.virtualRepeat'
]);

})();
(function(){
"use strict";

/*
 * @ngdoc module
 * @name material.components.backdrop
 * @description Backdrop
 */

/**
 * @ngdoc directive
 * @name mdBackdrop
 * @module material.components.backdrop
 *
 * @restrict E
 *
 * @description
 * `<md-backdrop>` is a backdrop element used by other components, such as dialog and bottom sheet.
 * Apply class `opaque` to make the backdrop use the theme backdrop color.
 *
 */

angular
  .module('material.components.backdrop', ['material.core'])
  .directive('mdBackdrop', ["$mdTheming", "$mdUtil", "$animate", "$rootElement", "$window", "$log", "$$rAF", "$document", function BackdropDirective($mdTheming, $mdUtil, $animate, $rootElement, $window, $log, $$rAF, $document) {
    var ERROR_CSS_POSITION = '<md-backdrop> may not work properly in a scrolled, static-positioned parent container.';

    return {
      restrict: 'E',
      link: postLink
    };

    function postLink(scope, element, attrs) {
      // backdrop may be outside the $rootElement, tell ngAnimate to animate regardless
      if ($animate.pin) $animate.pin(element, $rootElement);

      var bodyStyles;

      $$rAF(function() {
        // If body scrolling has been disabled using mdUtil.disableBodyScroll(),
        // adjust the 'backdrop' height to account for the fixed 'body' top offset.
        // Note that this can be pretty expensive and is better done inside the $$rAF.
        bodyStyles = $window.getComputedStyle($document[0].body);

        if (bodyStyles.position === 'fixed') {
          var resizeHandler = $mdUtil.debounce(function(){
            bodyStyles = $window.getComputedStyle($document[0].body);
            resize();
          }, 60, null, false);

          resize();
          angular.element($window).on('resize', resizeHandler);

          scope.$on('$destroy', function() {
            angular.element($window).off('resize', resizeHandler);
          });
        }

        // Often $animate.enter() is used to append the backDrop element
        // so let's wait until $animate is done...
        var parent = element.parent();

        if (parent.length) {
          if (parent[0].nodeName === 'BODY') {
            element.css('position', 'fixed');
          }

          var styles = $window.getComputedStyle(parent[0]);

          if (styles.position === 'static') {
            // backdrop uses position:absolute and will not work properly with parent position:static (default)
            $log.warn(ERROR_CSS_POSITION);
          }

          // Only inherit the parent if the backdrop has a parent.
          $mdTheming.inherit(element, parent);
        }
      });

      function resize() {
        var viewportHeight = parseInt(bodyStyles.height, 10) + Math.abs(parseInt(bodyStyles.top, 10));
        element.css('height', viewportHeight + 'px');
      }
    }

  }]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.bottomSheet
 * @description
 * BottomSheet
 */
MdBottomSheetDirective.$inject = ["$mdBottomSheet"];
MdBottomSheetProvider.$inject = ["$$interimElementProvider"];
angular
  .module('material.components.bottomSheet', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdBottomSheet', MdBottomSheetDirective)
  .provider('$mdBottomSheet', MdBottomSheetProvider);

/* @ngInject */
function MdBottomSheetDirective($mdBottomSheet) {
  return {
    restrict: 'E',
    link : function postLink(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      // When navigation force destroys an interimElement, then
      // listen and $destroy() that interim instance...
      scope.$on('$destroy', function() {
        $mdBottomSheet.destroy();
      });
    }
  };
}


/**
 * @ngdoc service
 * @name $mdBottomSheet
 * @module material.components.bottomSheet
 *
 * @description
 * `$mdBottomSheet` opens a bottom sheet over the app and provides a simple promise API.
 *
 * ## Restrictions
 *
 * - The bottom sheet's template must have an outer `<md-bottom-sheet>` element.
 * - Add the `md-grid` class to the bottom sheet for a grid layout.
 * - Add the `md-list` class to the bottom sheet for a list layout.
 *
 * @usage
 * <hljs lang="html">
 * <div ng-controller="MyController">
 *   <md-button ng-click="openBottomSheet()">
 *     Open a Bottom Sheet!
 *   </md-button>
 * </div>
 * </hljs>
 * <hljs lang="js">
 * var app = angular.module('app', ['ngMaterial']);
 * app.controller('MyController', function($scope, $mdBottomSheet) {
 *   $scope.openBottomSheet = function() {
 *     $mdBottomSheet.show({
 *       template: '<md-bottom-sheet>' +
 *       'Hello! <md-button ng-click="closeBottomSheet()">Close</md-button>' +
 *       '</md-bottom-sheet>'
 *     })
 *
 *     // Fires when the hide() method is used
 *     .then(function() {
 *       console.log('You clicked the button to close the bottom sheet!');
 *     })
 *
 *     // Fires when the cancel() method is used
 *     .catch(function() {
 *       console.log('You hit escape or clicked the backdrop to close.');
 *     });
 *   };
 *
 *   $scope.closeBottomSheet = function($scope, $mdBottomSheet) {
 *     $mdBottomSheet.hide();
 *   }
 *
 * });
 * </hljs>
 */

 /**
 * @ngdoc method
 * @name $mdBottomSheet#show
 *
 * @description
 * Show a bottom sheet with the specified options.
 *
 * <em><b>Note:</b> You should <b>always</b> provide a `.catch()` method in case the user hits the
 * `esc` key or clicks the background to close. In this case, the `cancel()` method will
 * automatically be called on the bottom sheet which will `reject()` the promise. See the @usage
 * section above for an example.
 *
 * Newer versions of Angular will throw a `Possibly unhandled rejection` exception if you forget
 * this.</em>
 *
 * @param {object} options An options object, with the following properties:
 *
 *   - `templateUrl` - `{string=}`: The url of an html template file that will
 *   be used as the content of the bottom sheet. Restrictions: the template must
 *   have an outer `md-bottom-sheet` element.
 *   - `template` - `{string=}`: Same as templateUrl, except this is an actual
 *   template string.
 *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified, it will create a new child scope.
 *     This scope will be destroyed when the bottom sheet is removed unless `preserveScope` is set to true.
 *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed. Default is false
 *   - `controller` - `{string=}`: The controller to associate with this bottom sheet.
 *   - `locals` - `{string=}`: An object containing key/value pairs. The keys will
 *   be used as names of values to inject into the controller. For example,
 *   `locals: {three: 3}` would inject `three` into the controller with the value
 *   of 3.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click outside the bottom sheet to
 *     close it. Default true.
 *   - `bindToController` - `{boolean=}`: When set to true, the locals will be bound to the controller instance.
 *   - `disableBackdrop` - `{boolean=}`: When set to true, the bottomsheet will not show a backdrop.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to close the bottom sheet.
 *     Default true.
 *   - `resolve` - `{object=}`: Similar to locals, except it takes promises as values
 *   and the bottom sheet will not open until the promises resolve.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
 *   - `parent` - `{element=}`: The element to append the bottom sheet to. The `parent` may be a `function`, `string`,
 *   `object`, or null. Defaults to appending to the body of the root element (or the root element) of the application.
 *   e.g. angular.element(document.getElementById('content')) or "#content"
 *   - `disableParentScroll` - `{boolean=}`: Whether to disable scrolling while the bottom sheet is open.
 *     Default true.
 *
 * @returns {promise} A promise that can be resolved with `$mdBottomSheet.hide()` or
 * rejected with `$mdBottomSheet.cancel()`.
 */

/**
 * @ngdoc method
 * @name $mdBottomSheet#hide
 *
 * @description
 * Hide the existing bottom sheet and resolve the promise returned from
 * `$mdBottomSheet.show()`. This call will close the most recently opened/current bottomsheet (if
 * any).
 *
 * <em><b>Note:</b> Use a `.then()` on your `.show()` to handle this callback.</em>
 *
 * @param {*=} response An argument for the resolved promise.
 *
 */

/**
 * @ngdoc method
 * @name $mdBottomSheet#cancel
 *
 * @description
 * Hide the existing bottom sheet and reject the promise returned from
 * `$mdBottomSheet.show()`.
 *
 * <em><b>Note:</b> Use a `.catch()` on your `.show()` to handle this callback.</em>
 *
 * @param {*=} response An argument for the rejected promise.
 *
 */

function MdBottomSheetProvider($$interimElementProvider) {
  // how fast we need to flick down to close the sheet, pixels/ms
  bottomSheetDefaults.$inject = ["$animate", "$mdConstant", "$mdUtil", "$mdTheming", "$mdBottomSheet", "$rootElement", "$mdGesture", "$log"];
  var CLOSING_VELOCITY = 0.5;
  var PADDING = 80; // same as css

  return $$interimElementProvider('$mdBottomSheet')
    .setDefaults({
      methods: ['disableParentScroll', 'escapeToClose', 'clickOutsideToClose'],
      options: bottomSheetDefaults
    });

  /* @ngInject */
  function bottomSheetDefaults($animate, $mdConstant, $mdUtil, $mdTheming, $mdBottomSheet, $rootElement,
                               $mdGesture, $log) {
    var backdrop;

    return {
      themable: true,
      onShow: onShow,
      onRemove: onRemove,
      disableBackdrop: false,
      escapeToClose: true,
      clickOutsideToClose: true,
      disableParentScroll: true
    };


    function onShow(scope, element, options, controller) {

      element = $mdUtil.extractElementByName(element, 'md-bottom-sheet');

      // prevent tab focus or click focus on the bottom-sheet container
      element.attr('tabindex',"-1");

      // Once the md-bottom-sheet has `ng-cloak` applied on his template the opening animation will not work properly.
      // This is a very common problem, so we have to notify the developer about this.
      if (element.hasClass('ng-cloak')) {
        var message = '$mdBottomSheet: using `<md-bottom-sheet ng-cloak >` will affect the bottom-sheet opening animations.';
        $log.warn( message, element[0] );
      }

      if (!options.disableBackdrop) {
        // Add a backdrop that will close on click
        backdrop = $mdUtil.createBackdrop(scope, "md-bottom-sheet-backdrop md-opaque");

        // Prevent mouse focus on backdrop; ONLY programatic focus allowed.
        // This allows clicks on backdrop to propogate to the $rootElement and
        // ESC key events to be detected properly.
        
        backdrop[0].tabIndex = -1;

        if (options.clickOutsideToClose) {
          backdrop.on('click', function() {
            $mdUtil.nextTick($mdBottomSheet.cancel,true);
          });
        }

        $mdTheming.inherit(backdrop, options.parent);

        $animate.enter(backdrop, options.parent, null);
      }

      var bottomSheet = new BottomSheet(element, options.parent);
      options.bottomSheet = bottomSheet;

      $mdTheming.inherit(bottomSheet.element, options.parent);

      if (options.disableParentScroll) {
        options.restoreScroll = $mdUtil.disableScrollAround(bottomSheet.element, options.parent);
      }

      return $animate.enter(bottomSheet.element, options.parent, backdrop)
        .then(function() {
          var focusable = $mdUtil.findFocusTarget(element) || angular.element(
            element[0].querySelector('button') ||
            element[0].querySelector('a') ||
            element[0].querySelector($mdUtil.prefixer('ng-click', true))
          ) || backdrop;

          if (options.escapeToClose) {
            options.rootElementKeyupCallback = function(e) {
              if (e.keyCode === $mdConstant.KEY_CODE.ESCAPE) {
                $mdUtil.nextTick($mdBottomSheet.cancel,true);
              }
            };

            $rootElement.on('keyup', options.rootElementKeyupCallback);
            focusable && focusable.focus();
          }
        });

    }

    function onRemove(scope, element, options) {

      var bottomSheet = options.bottomSheet;

      if (!options.disableBackdrop) $animate.leave(backdrop);
      return $animate.leave(bottomSheet.element).then(function() {
        if (options.disableParentScroll) {
          options.restoreScroll();
          delete options.restoreScroll;
        }

        bottomSheet.cleanup();
      });
    }

    /**
     * BottomSheet class to apply bottom-sheet behavior to an element
     */
    function BottomSheet(element, parent) {
      var deregister = $mdGesture.register(parent, 'drag', { horizontal: false });
      parent.on('$md.dragstart', onDragStart)
        .on('$md.drag', onDrag)
        .on('$md.dragend', onDragEnd);

      return {
        element: element,
        cleanup: function cleanup() {
          deregister();
          parent.off('$md.dragstart', onDragStart);
          parent.off('$md.drag', onDrag);
          parent.off('$md.dragend', onDragEnd);
        }
      };

      function onDragStart(ev) {
        // Disable transitions on transform so that it feels fast
        element.css($mdConstant.CSS.TRANSITION_DURATION, '0ms');
      }

      function onDrag(ev) {
        var transform = ev.pointer.distanceY;
        if (transform < 5) {
          // Slow down drag when trying to drag up, and stop after PADDING
          transform = Math.max(-PADDING, transform / 2);
        }
        element.css($mdConstant.CSS.TRANSFORM, 'translate3d(0,' + (PADDING + transform) + 'px,0)');
      }

      function onDragEnd(ev) {
        if (ev.pointer.distanceY > 0 &&
            (ev.pointer.distanceY > 20 || Math.abs(ev.pointer.velocityY) > CLOSING_VELOCITY)) {
          var distanceRemaining = element.prop('offsetHeight') - ev.pointer.distanceY;
          var transitionDuration = Math.min(distanceRemaining / ev.pointer.velocityY * 0.75, 500);
          element.css($mdConstant.CSS.TRANSITION_DURATION, transitionDuration + 'ms');
          $mdUtil.nextTick($mdBottomSheet.cancel,true);
        } else {
          element.css($mdConstant.CSS.TRANSITION_DURATION, '');
          element.css($mdConstant.CSS.TRANSFORM, '');
        }
      }
    }

  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.button
 * @description
 *
 * Button
 */
MdButtonDirective.$inject = ["$mdButtonInkRipple", "$mdTheming", "$mdAria", "$mdInteraction"];
MdAnchorDirective.$inject = ["$mdTheming"];
angular
    .module('material.components.button', [ 'material.core' ])
    .directive('mdButton', MdButtonDirective)
    .directive('a', MdAnchorDirective);


/**
 * @private
 * @restrict E
 *
 * @description
 * `a` is an anchor directive used to inherit theme colors for md-primary, md-accent, etc.
 *
 * @usage
 *
 * <hljs lang="html">
 *  <md-content md-theme="myTheme">
 *    <a href="#chapter1" class="md-accent"></a>
 *  </md-content>
 * </hljs>
 */
function MdAnchorDirective($mdTheming) {
  return {
    restrict : 'E',
    link : function postLink(scope, element) {
      // Make sure to inherit theme so stand-alone anchors
      // support theme colors for md-primary, md-accent, etc.
      $mdTheming(element);
    }
  };
}


/**
 * @ngdoc directive
 * @name mdButton
 * @module material.components.button
 *
 * @restrict E
 *
 * @description
 * `<md-button>` is a button directive with optional ink ripples (default enabled).
 *
 * If you supply a `href` or `ng-href` attribute, it will become an `<a>` element. Otherwise, it
 * will become a `<button>` element. As per the
 * [Material Design specifications](https://material.google.com/style/color.html#color-color-palette)
 * the FAB button background is filled with the accent color [by default]. The primary color palette
 * may be used with the `md-primary` class.
 *
 * Developers can also change the color palette of the button, by using the following classes
 * - `md-primary`
 * - `md-accent`
 * - `md-warn`
 *
 * See for example
 *
 * <hljs lang="html">
 *   <md-button class="md-primary">Primary Button</md-button>
 * </hljs>
 *
 * Button can be also raised, which means that they will use the current color palette to fill the button.
 *
 * <hljs lang="html">
 *   <md-button class="md-accent md-raised">Raised and Accent Button</md-button>
 * </hljs>
 *
 * It is also possible to disable the focus effect on the button, by using the following markup.
 *
 * <hljs lang="html">
 *   <md-button class="md-no-focus">No Focus Style</md-button>
 * </hljs>
 *
 * @param {boolean=} md-no-ink If present, disable ripple ink effects.
 * @param {expression=} ng-disabled En/Disable based on the expression
 * @param {string=} md-ripple-size Overrides the default ripple size logic. Options: `full`, `partial`, `auto`
 * @param {string=} aria-label Adds alternative text to button for accessibility, useful for icon buttons.
 * If no default text is found, a warning will be logged.
 *
 * @usage
 *
 * Regular buttons:
 *
 * <hljs lang="html">
 *  <md-button> Flat Button </md-button>
 *  <md-button href="http://google.com"> Flat link </md-button>
 *  <md-button class="md-raised"> Raised Button </md-button>
 *  <md-button ng-disabled="true"> Disabled Button </md-button>
 *  <md-button>
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *    Register Now
 *  </md-button>
 * </hljs>
 *
 * FAB buttons:
 *
 * <hljs lang="html">
 *  <md-button class="md-fab" aria-label="FAB">
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *  </md-button>
 *  <!-- mini-FAB -->
 *  <md-button class="md-fab md-mini" aria-label="Mini FAB">
 *    <md-icon md-svg-src="your/icon.svg"></md-icon>
 *  </md-button>
 *  <!-- Button with SVG Icon -->
 *  <md-button class="md-icon-button" aria-label="Custom Icon Button">
 *    <md-icon md-svg-icon="path/to/your.svg"></md-icon>
 *  </md-button>
 * </hljs>
 */
function MdButtonDirective($mdButtonInkRipple, $mdTheming, $mdAria, $mdInteraction) {

  return {
    restrict: 'EA',
    replace: true,
    transclude: true,
    template: getTemplate,
    link: postLink
  };

  function isAnchor(attr) {
    return angular.isDefined(attr.href) || angular.isDefined(attr.ngHref) || angular.isDefined(attr.ngLink) || angular.isDefined(attr.uiSref);
  }

  function getTemplate(element, attr) {
    if (isAnchor(attr)) {
      return '<a class="md-button" ng-transclude></a>';
    } else {
      //If buttons don't have type="button", they will submit forms automatically.
      var btnType = (typeof attr.type === 'undefined') ? 'button' : attr.type;
      return '<button class="md-button" type="' + btnType + '" ng-transclude></button>';
    }
  }

  function postLink(scope, element, attr) {
    $mdTheming(element);
    $mdButtonInkRipple.attach(scope, element);

    // Use async expect to support possible bindings in the button label
    $mdAria.expectWithoutText(element, 'aria-label');

    // For anchor elements, we have to set tabindex manually when the
    // element is disabled
    if (isAnchor(attr) && angular.isDefined(attr.ngDisabled) ) {
      scope.$watch(attr.ngDisabled, function(isDisabled) {
        element.attr('tabindex', isDisabled ? -1 : 0);
      });
    }

    // disabling click event when disabled is true
    element.on('click', function(e){
      if (attr.disabled === true) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });

    if (!element.hasClass('md-no-focus')) {

      element.on('focus', function() {

        // Only show the focus effect when being focused through keyboard interaction or programmatically
        if (!$mdInteraction.isUserInvoked() || $mdInteraction.getLastInteractionType() === 'keyboard') {
          element.addClass('md-focused');
        }

      });

      element.on('blur', function() {
        element.removeClass('md-focused');
      });
    }

  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.card
 *
 * @description
 * Card components.
 */
mdCardDirective.$inject = ["$mdTheming"];
angular.module('material.components.card', [
    'material.core'
  ])
  .directive('mdCard', mdCardDirective);


/**
 * @ngdoc directive
 * @name mdCard
 * @module material.components.card
 *
 * @restrict E
 *
 * @description
 * The `<md-card>` directive is a container element used within `<md-content>` containers.
 *
 * An image included as a direct descendant will fill the card's width. If you want to avoid this,
 * you can add the `md-image-no-fill` class to the parent element. The `<md-card-content>`
 * container will wrap text content and provide padding. An `<md-card-footer>` element can be
 * optionally included to put content flush against the bottom edge of the card.
 *
 * Action buttons can be included in an `<md-card-actions>` element, similar to `<md-dialog-actions>`.
 * You can then position buttons using layout attributes.
 *
 * Card is built with:
 * * `<md-card-header>` - Header for the card, holds avatar, text and squared image
 *  - `<md-card-avatar>` - Card avatar
 *    - `md-user-avatar` - Class for user image
 *    - `<md-icon>`
 *  - `<md-card-header-text>` - Contains elements for the card description
 *    - `md-title` - Class for the card title
 *    - `md-subhead` - Class for the card sub header
 * * `<img>` - Image for the card
 * * `<md-card-title>` - Card content title
 *  - `<md-card-title-text>`
 *    - `md-headline` - Class for the card content title
 *    - `md-subhead` - Class for the card content sub header
 *  - `<md-card-title-media>` - Squared image within the title
 *    - `md-media-sm` - Class for small image
 *    - `md-media-md` - Class for medium image
 *    - `md-media-lg` - Class for large image
 *    - `md-media-xl` - Class for extra large image
 * * `<md-card-content>` - Card content
 * * `<md-card-actions>` - Card actions
 *  - `<md-card-icon-actions>` - Icon actions
 *
 * Cards have constant width and variable heights; where the maximum height is limited to what can
 * fit within a single view on a platform, but it can temporarily expand as needed.
 *
 * @usage
 * ### Card with optional footer
 * <hljs lang="html">
 * <md-card>
 *  <img src="card-image.png" class="md-card-image" alt="image caption">
 *  <md-card-content>
 *    <h2>Card headline</h2>
 *    <p>Card content</p>
 *  </md-card-content>
 *  <md-card-footer>
 *    Card footer
 *  </md-card-footer>
 * </md-card>
 * </hljs>
 *
 * ### Card with actions
 * <hljs lang="html">
 * <md-card>
 *  <img src="card-image.png" class="md-card-image" alt="image caption">
 *  <md-card-content>
 *    <h2>Card headline</h2>
 *    <p>Card content</p>
 *  </md-card-content>
 *  <md-card-actions layout="row" layout-align="end center">
 *    <md-button>Action 1</md-button>
 *    <md-button>Action 2</md-button>
 *  </md-card-actions>
 * </md-card>
 * </hljs>
 *
 * ### Card with header, image, title actions and content
 * <hljs lang="html">
 * <md-card>
 *   <md-card-header>
 *     <md-card-avatar>
 *       <img class="md-user-avatar" src="avatar.png"/>
 *     </md-card-avatar>
 *     <md-card-header-text>
 *       <span class="md-title">Title</span>
 *       <span class="md-subhead">Sub header</span>
 *     </md-card-header-text>
 *   </md-card-header>
 *   <img ng-src="card-image.png" class="md-card-image" alt="image caption">
 *   <md-card-title>
 *     <md-card-title-text>
 *       <span class="md-headline">Card headline</span>
 *       <span class="md-subhead">Card subheader</span>
 *     </md-card-title-text>
 *   </md-card-title>
 *   <md-card-actions layout="row" layout-align="start center">
 *     <md-button>Action 1</md-button>
 *     <md-button>Action 2</md-button>
 *     <md-card-icon-actions>
 *       <md-button class="md-icon-button" aria-label="icon">
 *         <md-icon md-svg-icon="icon"></md-icon>
 *       </md-button>
 *     </md-card-icon-actions>
 *   </md-card-actions>
 *   <md-card-content>
 *     <p>
 *      Card content
 *     </p>
 *   </md-card-content>
 * </md-card>
 * </hljs>
 */
function mdCardDirective($mdTheming) {
  return {
    restrict: 'E',
    link: function ($scope, $element, attr) {
      $element.addClass('_md');     // private md component indicator for styling
      $mdTheming($element);
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.chips
 */
/*
 * @see js folder for chips implementation
 */
angular.module('material.components.chips', [
  'material.core',
  'material.components.autocomplete'
]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.checkbox
 * @description Checkbox module!
 */
MdCheckboxDirective.$inject = ["inputDirective", "$mdAria", "$mdConstant", "$mdTheming", "$mdUtil", "$mdInteraction"];
angular
  .module('material.components.checkbox', ['material.core'])
  .directive('mdCheckbox', MdCheckboxDirective);

/**
 * @ngdoc directive
 * @name mdCheckbox
 * @module material.components.checkbox
 * @restrict E
 *
 * @description
 * The checkbox directive is used like the normal [angular checkbox](https://docs.angularjs.org/api/ng/input/input%5Bcheckbox%5D).
 *
 * As per the [material design spec](http://www.google.com/design/spec/style/color.html#color-color-schemes)
 * the checkbox is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * @param {string} ng-model Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {expression=} ng-true-value The value to which the expression should be set when selected.
 * @param {expression=} ng-false-value The value to which the expression should be set when not selected.
 * @param {string=} ng-change AngularJS expression to be executed when input changes due to user interaction with the input element.
 * @param {boolean=} md-no-ink Use of attribute indicates use of ripple ink effects
 * @param {string=} aria-label Adds label to checkbox for accessibility.
 *     Defaults to checkbox's text. If no default text is found, a warning will be logged.
 * @param {expression=} md-indeterminate This determines when the checkbox should be rendered as 'indeterminate'.
 *     If a truthy expression or no value is passed in the checkbox renders in the md-indeterminate state.
 *     If falsy expression is passed in it just looks like a normal unchecked checkbox.
 *     The indeterminate, checked, and unchecked states are mutually exclusive. A box cannot be in any two states at the same time.
 *     Adding the 'md-indeterminate' attribute overrides any checked/unchecked rendering logic.
 *     When using the 'md-indeterminate' attribute use 'ng-checked' to define rendering logic instead of using 'ng-model'.
 * @param {expression=} ng-checked If this expression evaluates as truthy, the 'md-checked' css class is added to the checkbox and it
 *     will appear checked.
 *
 * @usage
 * <hljs lang="html">
 * <md-checkbox ng-model="isChecked" aria-label="Finished?">
 *   Finished ?
 * </md-checkbox>
 *
 * <md-checkbox md-no-ink ng-model="hasInk" aria-label="No Ink Effects">
 *   No Ink Effects
 * </md-checkbox>
 *
 * <md-checkbox ng-disabled="true" ng-model="isDisabled" aria-label="Disabled">
 *   Disabled
 * </md-checkbox>
 *
 * </hljs>
 *
 */
function MdCheckboxDirective(inputDirective, $mdAria, $mdConstant, $mdTheming, $mdUtil, $mdInteraction) {
  inputDirective = inputDirective[0];

  return {
    restrict: 'E',
    transclude: true,
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    priority: $mdConstant.BEFORE_NG_ARIA,
    template:
      '<div class="md-container" md-ink-ripple md-ink-ripple-checkbox>' +
        '<div class="md-icon"></div>' +
      '</div>' +
      '<div ng-transclude class="md-label"></div>',
    compile: compile
  };

  // **********************************************************
  // Private Methods
  // **********************************************************

  function compile (tElement, tAttrs) {
    tAttrs.$set('tabindex', tAttrs.tabindex || '0');
    tAttrs.$set('type', 'checkbox');
    tAttrs.$set('role', tAttrs.type);

    return  {
      pre: function(scope, element) {
        // Attach a click handler during preLink, in order to immediately stop propagation
        // (especially for ng-click) when the checkbox is disabled.
        element.on('click', function(e) {
          if (this.hasAttribute('disabled')) {
            e.stopImmediatePropagation();
          }
        });
      },
      post: postLink
    };

    function postLink(scope, element, attr, ctrls) {
      var isIndeterminate;
      var containerCtrl = ctrls[0];
      var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();
      var formCtrl = ctrls[2];

      if (containerCtrl) {
        var isErrorGetter = containerCtrl.isErrorGetter || function() {
          return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (formCtrl && formCtrl.$submitted));
        };

        containerCtrl.input = element;

        scope.$watch(isErrorGetter, containerCtrl.setInvalid);
      }

      $mdTheming(element);

      // Redirect focus events to the root element, because IE11 is always focusing the container element instead
      // of the md-checkbox element. This causes issues when using ngModelOptions: `updateOnBlur`
      element.children().on('focus', function() {
        element.focus();
      });

      if ($mdUtil.parseAttributeBoolean(attr.mdIndeterminate)) {
        setIndeterminateState();
        scope.$watch(attr.mdIndeterminate, setIndeterminateState);
      }

      if (attr.ngChecked) {
        scope.$watch(scope.$eval.bind(scope, attr.ngChecked), function(value) {
          ngModelCtrl.$setViewValue(value);
          ngModelCtrl.$render();
        });
      }

      $$watchExpr('ngDisabled', 'tabindex', {
        true: '-1',
        false: attr.tabindex
      });

      $mdAria.expectWithText(element, 'aria-label');

      // Reuse the original input[type=checkbox] directive from AngularJS core.
      // This is a bit hacky as we need our own event listener and own render
      // function.
      inputDirective.link.pre(scope, {
        on: angular.noop,
        0: {}
      }, attr, [ngModelCtrl]);

      element.on('click', listener)
        .on('keypress', keypressHandler)
        .on('focus', function() {
          if ($mdInteraction.getLastInteractionType() === 'keyboard') {
            element.addClass('md-focused');
          }
        })
        .on('blur', function() {
          element.removeClass('md-focused');
        });

      ngModelCtrl.$render = render;

      function $$watchExpr(expr, htmlAttr, valueOpts) {
        if (attr[expr]) {
          scope.$watch(attr[expr], function(val) {
            if (valueOpts[val]) {
              element.attr(htmlAttr, valueOpts[val]);
            }
          });
        }
      }

      function keypressHandler(ev) {
        var keyCode = ev.which || ev.keyCode;
        if (keyCode === $mdConstant.KEY_CODE.SPACE || keyCode === $mdConstant.KEY_CODE.ENTER) {
          ev.preventDefault();
          element.addClass('md-focused');
          listener(ev);
        }
      }

      function listener(ev) {
        // skipToggle boolean is used by the switch directive to prevent the click event
        // when releasing the drag. There will be always a click if releasing the drag over the checkbox
        if (element[0].hasAttribute('disabled') || scope.skipToggle) {
          return;
        }

        scope.$apply(function() {
          // Toggle the checkbox value...
          var viewValue = attr.ngChecked && attr.ngClick ? attr.checked : !ngModelCtrl.$viewValue;

          ngModelCtrl.$setViewValue(viewValue, ev && ev.type);
          ngModelCtrl.$render();
        });
      }

      function render() {
        // Cast the $viewValue to a boolean since it could be undefined
        element.toggleClass('md-checked', !!ngModelCtrl.$viewValue && !isIndeterminate);
      }

      function setIndeterminateState(newValue) {
        isIndeterminate = newValue !== false;
        if (isIndeterminate) {
          element.attr('aria-checked', 'mixed');
        }
        element.toggleClass('md-indeterminate', isIndeterminate);
      }
    }
  }
}

})();
(function(){
"use strict";

(function () {
  "use strict";

  /**
   *  Use a RegExp to check if the `md-colors="<expression>"` is static string
   *  or one that should be observed and dynamically interpolated.
   */
  MdColorsDirective.$inject = ["$mdColors", "$mdUtil", "$log", "$parse"];
  MdColorsService.$inject = ["$mdTheming", "$mdUtil", "$log"];
  var STATIC_COLOR_EXPRESSION = /^{((\s|,)*?["'a-zA-Z-]+?\s*?:\s*?('|")[a-zA-Z0-9-.]*('|"))+\s*}$/;
  var colorPalettes = null;

  /**
   * @ngdoc module
   * @name material.components.colors
   *
   * @description
   * Define $mdColors service and a `md-colors=""` attribute directive
   */
  angular
    .module('material.components.colors', ['material.core'])
    .directive('mdColors', MdColorsDirective)
    .service('$mdColors', MdColorsService);

  /**
   * @ngdoc service
   * @name $mdColors
   * @module material.components.colors
   *
   * @description
   * With only defining themes, one couldn't get non AngularJS Material elements colored with Material colors,
   * `$mdColors` service is used by the md-color directive to convert the 1..n color expressions to RGBA values and will apply
   * those values to element as CSS property values.
   *
   *  @usage
   *  <hljs lang="js">
   *    angular.controller('myCtrl', function ($mdColors) {
   *      var color = $mdColors.getThemeColor('myTheme-red-200-0.5');
   *      ...
   *    });
   *  </hljs>
   *
   */
  function MdColorsService($mdTheming, $mdUtil, $log) {
    colorPalettes = colorPalettes || Object.keys($mdTheming.PALETTES);

    // Publish service instance
    return {
      applyThemeColors: applyThemeColors,
      getThemeColor: getThemeColor,
      hasTheme: hasTheme
    };

    // ********************************************
    // Internal Methods
    // ********************************************

    /**
     * @ngdoc method
     * @name $mdColors#applyThemeColors
     *
     * @description
     * Gets a color json object, keys are css properties and values are string of the wanted color
     * Then calculate the rgba() values based on the theme color parts
     *
     * @param {DOMElement} element the element to apply the styles on.
     * @param {object} colorExpression json object, keys are css properties and values are string of the wanted color,
     * for example: `{color: 'red-A200-0.3'}`.
     *
     * @usage
     * <hljs lang="js">
     *   app.directive('myDirective', function($mdColors) {
     *     return {
     *       ...
     *       link: function (scope, elem) {
     *         $mdColors.applyThemeColors(elem, {color: 'red'});
     *       }
     *    }
     *   });
     * </hljs>
     */
    function applyThemeColors(element, colorExpression) {
      try {
        if (colorExpression) {
          // Assign the calculate RGBA color values directly as inline CSS
          element.css(interpolateColors(colorExpression));
        }
      } catch (e) {
        $log.error(e.message);
      }

    }

    /**
     * @ngdoc method
     * @name $mdColors#getThemeColor
     *
     * @description
     * Get parsed color from expression
     *
     * @param {string} expression string of a color expression (for instance `'red-700-0.8'`)
     *
     * @returns {string} a css color expression (for instance `rgba(211, 47, 47, 0.8)`)
     *
     * @usage
     *  <hljs lang="js">
     *    angular.controller('myCtrl', function ($mdColors) {
     *      var color = $mdColors.getThemeColor('myTheme-red-200-0.5');
     *      ...
     *    });
     *  </hljs>
     */
    function getThemeColor(expression) {
      var color = extractColorOptions(expression);

      return parseColor(color);
    }

    /**
     * Return the parsed color
     * @param color hashmap of color definitions
     * @param contrast whether use contrast color for foreground
     * @returns rgba color string
     */
    function parseColor(color, contrast) {
      contrast = contrast || false;
      var rgbValues = $mdTheming.PALETTES[color.palette][color.hue];

      rgbValues = contrast ? rgbValues.contrast : rgbValues.value;

      return $mdUtil.supplant('rgba({0}, {1}, {2}, {3})',
        [rgbValues[0], rgbValues[1], rgbValues[2], rgbValues[3] || color.opacity]
      );
    }

    /**
     * Convert the color expression into an object with scope-interpolated values
     * Then calculate the rgba() values based on the theme color parts
     *
     * @results Hashmap of CSS properties with associated `rgba( )` string vales
     *
     *
     */
    function interpolateColors(themeColors) {
      var rgbColors = {};

      var hasColorProperty = themeColors.hasOwnProperty('color');

      angular.forEach(themeColors, function (value, key) {
        var color = extractColorOptions(value);
        var hasBackground = key.indexOf('background') > -1;

        rgbColors[key] = parseColor(color);
        if (hasBackground && !hasColorProperty) {
          rgbColors.color = parseColor(color, true);
        }
      });

      return rgbColors;
    }

    /**
     * Check if expression has defined theme
     * e.g.
     * 'myTheme-primary' => true
     * 'red-800' => false
     */
    function hasTheme(expression) {
      return angular.isDefined($mdTheming.THEMES[expression.split('-')[0]]);
    }

    /**
     * For the evaluated expression, extract the color parts into a hash map
     */
    function extractColorOptions(expression) {
      var parts = expression.split('-');
      var hasTheme = angular.isDefined($mdTheming.THEMES[parts[0]]);
      var theme = hasTheme ? parts.splice(0, 1)[0] : $mdTheming.defaultTheme();

      return {
        theme: theme,
        palette: extractPalette(parts, theme),
        hue: extractHue(parts, theme),
        opacity: parts[2] || 1
      };
    }

    /**
     * Calculate the theme palette name
     */
    function extractPalette(parts, theme) {
      // If the next section is one of the palettes we assume it's a two word palette
      // Two word palette can be also written in camelCase, forming camelCase to dash-case

      var isTwoWord = parts.length > 1 && colorPalettes.indexOf(parts[1]) !== -1;
      var palette = parts[0].replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

      if (isTwoWord)  palette = parts[0] + '-' + parts.splice(1, 1);

      if (colorPalettes.indexOf(palette) === -1) {
        // If the palette is not in the palette list it's one of primary/accent/warn/background
        var scheme = $mdTheming.THEMES[theme].colors[palette];
        if (!scheme) {
          throw new Error($mdUtil.supplant('mdColors: couldn\'t find \'{palette}\' in the palettes.', {palette: palette}));
        }
        palette = scheme.name;
      }

      return palette;
    }

    function extractHue(parts, theme) {
      var themeColors = $mdTheming.THEMES[theme].colors;

      if (parts[1] === 'hue') {
        var hueNumber = parseInt(parts.splice(2, 1)[0], 10);

        if (hueNumber < 1 || hueNumber > 3) {
          throw new Error($mdUtil.supplant('mdColors: \'hue-{hueNumber}\' is not a valid hue, can be only \'hue-1\', \'hue-2\' and \'hue-3\'', {hueNumber: hueNumber}));
        }
        parts[1] = 'hue-' + hueNumber;

        if (!(parts[0] in themeColors)) {
          throw new Error($mdUtil.supplant('mdColors: \'hue-x\' can only be used with [{availableThemes}], but was used with \'{usedTheme}\'', {
            availableThemes: Object.keys(themeColors).join(', '),
            usedTheme: parts[0]
          }));
        }

        return themeColors[parts[0]].hues[parts[1]];
      }

      return parts[1] || themeColors[parts[0] in themeColors ? parts[0] : 'primary'].hues['default'];
    }
  }

  /**
   * @ngdoc directive
   * @name mdColors
   * @module material.components.colors
   *
   * @restrict A
   *
   * @description
   * `mdColors` directive will apply the theme-based color expression as RGBA CSS style values.
   *
   *   The format will be similar to our color defining in the scss files:
   *
   *   ## `[?theme]-[palette]-[?hue]-[?opacity]`
   *   - [theme]    - default value is the default theme
   *   - [palette]  - can be either palette name or primary/accent/warn/background
   *   - [hue]      - default is 500 (hue-x can be used with primary/accent/warn/background)
   *   - [opacity]  - default is 1
   *
   *   > `?` indicates optional parameter
   *
   * @usage
   * <hljs lang="html">
   *   <div md-colors="{background: 'myTheme-accent-900-0.43'}">
   *     <div md-colors="{color: 'red-A100', 'border-color': 'primary-600'}">
   *       <span>Color demo</span>
   *     </div>
   *   </div>
   * </hljs>
   *
   * `mdColors` directive will automatically watch for changes in the expression if it recognizes an interpolation
   * expression or a function. For performance options, you can use `::` prefix to the `md-colors` expression
   * to indicate a one-time data binding.
   * <hljs lang="html">
   *   <md-card md-colors="::{background: '{{theme}}-primary-700'}">
   *   </md-card>
   * </hljs>
   *
   */
  function MdColorsDirective($mdColors, $mdUtil, $log, $parse) {
    return {
      restrict: 'A',
      require: ['^?mdTheme'],
      compile: function (tElem, tAttrs) {
        var shouldWatch = shouldColorsWatch();

        return function (scope, element, attrs, ctrl) {
          var mdThemeController = ctrl[0];

          var lastColors = {};

          var parseColors = function (theme) {
            if (typeof theme !== 'string') {
              theme = '';
            }

            if (!attrs.mdColors) {
              attrs.mdColors = '{}';
            }

            /**
             * Json.parse() does not work because the keys are not quoted;
             * use $parse to convert to a hash map
             */
            var colors = $parse(attrs.mdColors)(scope);

            /**
             * If mdTheme is defined up the DOM tree
             * we add mdTheme theme to colors who doesn't specified a theme
             *
             * # example
             * <hljs lang="html">
             *   <div md-theme="myTheme">
             *     <div md-colors="{background: 'primary-600'}">
             *       <span md-colors="{background: 'mySecondTheme-accent-200'}">Color demo</span>
             *     </div>
             *   </div>
             * </hljs>
             *
             * 'primary-600' will be 'myTheme-primary-600',
             * but 'mySecondTheme-accent-200' will stay the same cause it has a theme prefix
             */
            if (mdThemeController) {
              Object.keys(colors).forEach(function (prop) {
                var color = colors[prop];
                if (!$mdColors.hasTheme(color)) {
                  colors[prop] = (theme || mdThemeController.$mdTheme) + '-' + color;
                }
              });
            }

            cleanElement(colors);

            return colors;
          };

          var cleanElement = function (colors) {
            if (!angular.equals(colors, lastColors)) {
              var keys = Object.keys(lastColors);

              if (lastColors.background && !keys.color) {
                keys.push('color');
              }

              keys.forEach(function (key) {
                element.css(key, '');
              });
            }

            lastColors = colors;
          };

          /**
           * Registering for mgTheme changes and asking mdTheme controller run our callback whenever a theme changes
           */
          var unregisterChanges = angular.noop;

          if (mdThemeController) {
            unregisterChanges = mdThemeController.registerChanges(function (theme) {
              $mdColors.applyThemeColors(element, parseColors(theme));
            });
          }

          scope.$on('$destroy', function () {
            unregisterChanges();
          });

          try {
            if (shouldWatch) {
              scope.$watch(parseColors, angular.bind(this,
                $mdColors.applyThemeColors, element
              ), true);
            }
            else {
              $mdColors.applyThemeColors(element, parseColors());
            }

          }
          catch (e) {
            $log.error(e.message);
          }

        };

        function shouldColorsWatch() {
          // Simulate 1x binding and mark mdColorsWatch == false
          var rawColorExpression = tAttrs.mdColors;
          var bindOnce = rawColorExpression.indexOf('::') > -1;
          var isStatic = bindOnce ? true : STATIC_COLOR_EXPRESSION.test(tAttrs.mdColors);

          // Remove it for the postLink...
          tAttrs.mdColors = rawColorExpression.replace('::', '');

          var hasWatchAttr = angular.isDefined(tAttrs.mdColorsWatch);

          return (bindOnce || isStatic) ? false :
            hasWatchAttr ? $mdUtil.parseAttributeBoolean(tAttrs.mdColorsWatch) : true;
        }
      }
    };

  }


})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.content
 *
 * @description
 * Scrollable content
 */
mdContentDirective.$inject = ["$mdTheming"];
angular.module('material.components.content', [
  'material.core'
])
  .directive('mdContent', mdContentDirective);

/**
 * @ngdoc directive
 * @name mdContent
 * @module material.components.content
 *
 * @restrict E
 *
 * @description
 *
 * The `<md-content>` directive is a container element useful for scrollable content. It achieves
 * this by setting the CSS `overflow` property to `auto` so that content can properly scroll.
 *
 * In general, `<md-content>` components are not designed to be nested inside one another. If
 * possible, it is better to make them siblings. This often results in a better user experience as
 * having nested scrollbars may confuse the user.
 *
 * ## Troubleshooting
 *
 * In some cases, you may wish to apply the `md-no-momentum` class to ensure that Safari's
 * momentum scrolling is disabled. Momentum scrolling can cause flickering issues while scrolling
 * SVG icons and some other components.
 *
 * Additionally, we now also offer the `md-no-flicker` class which can be applied to any element
 * and uses a Webkit-specific filter of `blur(0px)` that forces GPU rendering of all elements
 * inside (which eliminates the flicker on iOS devices).
 *
 * _<b>Note:</b> Forcing an element to render on the GPU can have unintended side-effects, especially
 * related to the z-index of elements. Please use with caution and only on the elements needed._
 *
 * @usage
 *
 * Add the `[layout-padding]` attribute to make the content padded.
 *
 * <hljs lang="html">
 *  <md-content layout-padding>
 *      Lorem ipsum dolor sit amet, ne quod novum mei.
 *  </md-content>
 * </hljs>
 */

function mdContentDirective($mdTheming) {
  return {
    restrict: 'E',
    controller: ['$scope', '$element', ContentController],
    link: function(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      $mdTheming(element);
      scope.$broadcast('$mdContentLoaded', element);

      iosScrollFix(element[0]);
    }
  };

  function ContentController($scope, $element) {
    this.$scope = $scope;
    this.$element = $element;
  }
}

function iosScrollFix(node) {
  // IOS FIX:
  // If we scroll where there is no more room for the webview to scroll,
  // by default the webview itself will scroll up and down, this looks really
  // bad.  So if we are scrolling to the very top or bottom, add/subtract one
  angular.element(node).on('$md.pressdown', function(ev) {
    // Only touch events
    if (ev.pointer.type !== 't') return;
    // Don't let a child content's touchstart ruin it for us.
    if (ev.$materialScrollFixed) return;
    ev.$materialScrollFixed = true;

    if (node.scrollTop === 0) {
      node.scrollTop = 1;
    } else if (node.scrollHeight === node.scrollTop + node.offsetHeight) {
      node.scrollTop -= 1;
    }
  });
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.datepicker
 * @description Module for the datepicker component.
 */

angular.module('material.components.datepicker', [
  'material.core',
  'material.components.icon',
  'material.components.virtualRepeat'
]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.dialog
 */
MdDialogDirective.$inject = ["$$rAF", "$mdTheming", "$mdDialog"];
MdDialogProvider.$inject = ["$$interimElementProvider"];
angular
  .module('material.components.dialog', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdDialog', MdDialogDirective)
  .provider('$mdDialog', MdDialogProvider);

/**
 * @ngdoc directive
 * @name mdDialog
 * @module material.components.dialog
 *
 * @restrict E
 *
 * @description
 * `<md-dialog>` - The dialog's template must be inside this element.
 *
 * Inside, use an `<md-dialog-content>` element for the dialog's content, and use
 * an `<md-dialog-actions>` element for the dialog's actions.
 *
 * ## CSS
 * - `.md-dialog-content` - class that sets the padding on the content as the spec file
 *
 * ## Notes
 * - If you specify an `id` for the `<md-dialog>`, the `<md-dialog-content>` will have the same `id`
 * prefixed with `dialogContent_`.
 *
 * @usage
 * ### Dialog template
 * <hljs lang="html">
 * <md-dialog aria-label="List dialog">
 *   <md-dialog-content>
 *     <md-list>
 *       <md-list-item ng-repeat="item in items">
 *         <p>Number {{item}}</p>
 *       </md-list-item>
 *     </md-list>
 *   </md-dialog-content>
 *   <md-dialog-actions>
 *     <md-button ng-click="closeDialog()" class="md-primary">Close Dialog</md-button>
 *   </md-dialog-actions>
 * </md-dialog>
 * </hljs>
 */
function MdDialogDirective($$rAF, $mdTheming, $mdDialog) {
  return {
    restrict: 'E',
    link: function(scope, element) {
      element.addClass('_md');     // private md component indicator for styling

      $mdTheming(element);
      $$rAF(function() {
        var images;
        var content = element[0].querySelector('md-dialog-content');

        if (content) {
          images = content.getElementsByTagName('img');
          addOverflowClass();
          //-- delayed image loading may impact scroll height, check after images are loaded
          angular.element(images).on('load', addOverflowClass);
        }

        scope.$on('$destroy', function() {
          $mdDialog.destroy(element);
        });

        /**
         *
         */
        function addOverflowClass() {
          element.toggleClass('md-content-overflow', content.scrollHeight > content.clientHeight);
        }


      });
    }
  };
}

/**
 * @ngdoc service
 * @name $mdDialog
 * @module material.components.dialog
 *
 * @description
 * `$mdDialog` opens a dialog over the app to inform users about critical information or require
 *  them to make decisions. There are two approaches for setup: a simple promise API
 *  and regular object syntax.
 *
 * ## Restrictions
 *
 * - The dialog is always given an isolate scope.
 * - The dialog's template must have an outer `<md-dialog>` element.
 *   Inside, use an `<md-dialog-content>` element for the dialog's content, and use
 *   an `<md-dialog-actions>` element for the dialog's actions.
 * - Dialogs must cover the entire application to keep interactions inside of them.
 * Use the `parent` option to change where dialogs are appended.
 *
 * ## Sizing
 * - Complex dialogs can be sized with `flex="percentage"`, i.e. `flex="66"`.
 * - Default max-width is 80% of the `rootElement` or `parent`.
 *
 * ## CSS
 * - `.md-dialog-content` - class that sets the padding on the content as the spec file
 *
 * @usage
 * <hljs lang="html">
 * <div  ng-app="demoApp" ng-controller="EmployeeController">
 *   <div>
 *     <md-button ng-click="showAlert()" class="md-raised md-warn">
 *       Employee Alert!
 *       </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="showDialog($event)" class="md-raised">
 *       Custom Dialog
 *       </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="closeAlert()" ng-disabled="!hasAlert()" class="md-raised">
 *       Close Alert
 *     </md-button>
 *   </div>
 *   <div>
 *     <md-button ng-click="showGreeting($event)" class="md-raised md-primary" >
 *       Greet Employee
 *       </md-button>
 *   </div>
 * </div>
 * </hljs>
 *
 * ### JavaScript: object syntax
 * <hljs lang="js">
 * (function(angular, undefined){
 *   "use strict";
 *
 *   angular
 *    .module('demoApp', ['ngMaterial'])
 *    .controller('AppCtrl', AppController);
 *
 *   function AppController($scope, $mdDialog) {
 *     var alert;
 *     $scope.showAlert = showAlert;
 *     $scope.showDialog = showDialog;
 *     $scope.items = [1, 2, 3];
 *
 *     // Internal method
 *     function showAlert() {
 *       alert = $mdDialog.alert({
 *         title: 'Attention',
 *         textContent: 'This is an example of how easy dialogs can be!',
 *         ok: 'Close'
 *       });
 *
 *       $mdDialog
 *         .show( alert )
 *         .finally(function() {
 *           alert = undefined;
 *         });
 *     }
 *
 *     function showDialog($event) {
 *        var parentEl = angular.element(document.body);
 *        $mdDialog.show({
 *          parent: parentEl,
 *          targetEvent: $event,
 *          template:
 *            '<md-dialog aria-label="List dialog">' +
 *            '  <md-dialog-content>'+
 *            '    <md-list>'+
 *            '      <md-list-item ng-repeat="item in items">'+
 *            '       <p>Number {{item}}</p>' +
 *            '      </md-item>'+
 *            '    </md-list>'+
 *            '  </md-dialog-content>' +
 *            '  <md-dialog-actions>' +
 *            '    <md-button ng-click="closeDialog()" class="md-primary">' +
 *            '      Close Dialog' +
 *            '    </md-button>' +
 *            '  </md-dialog-actions>' +
 *            '</md-dialog>',
 *          locals: {
 *            items: $scope.items
 *          },
 *          controller: DialogController
 *       });
 *       function DialogController($scope, $mdDialog, items) {
 *         $scope.items = items;
 *         $scope.closeDialog = function() {
 *           $mdDialog.hide();
 *         }
 *       }
 *     }
 *   }
 * })(angular);
 * </hljs>
 *
 * ### Multiple Dialogs
 * Using the `multiple` option for the `$mdDialog` service allows developers to show multiple dialogs
 * at the same time.
 *
 * <hljs lang="js">
 *   // From plain options
 *   $mdDialog.show({
 *     multiple: true
 *   });
 *
 *   // From a dialog preset
 *   $mdDialog.show(
 *     $mdDialog
 *       .alert()
 *       .multiple(true)
 *   );
 *
 * </hljs>
 *
 * ### Pre-Rendered Dialogs
 * By using the `contentElement` option, it is possible to use an already existing element in the DOM.
 *
 * > Pre-rendered dialogs will be not linked to any scope and will not instantiate any new controller.<br/>
 * > You can manually link the elements to a scope or instantiate a controller from the template (`ng-controller`)
 *
 * <hljs lang="js">
 *   $scope.showPrerenderedDialog = function() {
 *     $mdDialog.show({
 *       contentElement: '#myStaticDialog',
 *       parent: angular.element(document.body)
 *     });
 *   };
 * </hljs>
 *
 * When using a string as value, `$mdDialog` will automatically query the DOM for the specified CSS selector.
 *
 * <hljs lang="html">
 *   <div style="visibility: hidden">
 *     <div class="md-dialog-container" id="myStaticDialog">
 *       <md-dialog>
 *         This is a pre-rendered dialog.
 *       </md-dialog>
 *     </div>
 *   </div>
 * </hljs>
 *
 * **Notice**: It is important, to use the `.md-dialog-container` as the content element, otherwise the dialog
 * will not show up.
 *
 * It also possible to use a DOM element for the `contentElement` option.
 * - `contentElement: document.querySelector('#myStaticDialog')`
 * - `contentElement: angular.element(TEMPLATE)`
 *
 * When using a `template` as content element, it will be not compiled upon open.
 * This allows you to compile the element yourself and use it each time the dialog opens.
 *
 * ### Custom Presets
 * Developers are also able to create their own preset, which can be easily used without repeating
 * their options each time.
 *
 * <hljs lang="js">
 *   $mdDialogProvider.addPreset('testPreset', {
 *     options: function() {
 *       return {
 *         template:
 *           '<md-dialog>' +
 *             'This is a custom preset' +
 *           '</md-dialog>',
 *         controllerAs: 'dialog',
 *         bindToController: true,
 *         clickOutsideToClose: true,
 *         escapeToClose: true
 *       };
 *     }
 *   });
 * </hljs>
 *
 * After you created your preset at config phase, you can easily access it.
 *
 * <hljs lang="js">
 *   $mdDialog.show(
 *     $mdDialog.testPreset()
 *   );
 * </hljs>
 *
 * ### JavaScript: promise API syntax, custom dialog template
 * <hljs lang="js">
 * (function(angular, undefined){
 *   "use strict";
 *
 *   angular
 *     .module('demoApp', ['ngMaterial'])
 *     .controller('EmployeeController', EmployeeEditor)
 *     .controller('GreetingController', GreetingController);
 *
 *   // Fictitious Employee Editor to show how to use simple and complex dialogs.
 *
 *   function EmployeeEditor($scope, $mdDialog) {
 *     var alert;
 *
 *     $scope.showAlert = showAlert;
 *     $scope.closeAlert = closeAlert;
 *     $scope.showGreeting = showCustomGreeting;
 *
 *     $scope.hasAlert = function() { return !!alert };
 *     $scope.userName = $scope.userName || 'Bobby';
 *
 *     // Dialog #1 - Show simple alert dialog and cache
 *     // reference to dialog instance
 *
 *     function showAlert() {
 *       alert = $mdDialog.alert()
 *         .title('Attention, ' + $scope.userName)
 *         .textContent('This is an example of how easy dialogs can be!')
 *         .ok('Close');
 *
 *       $mdDialog
 *           .show( alert )
 *           .finally(function() {
 *             alert = undefined;
 *           });
 *     }
 *
 *     // Close the specified dialog instance and resolve with 'finished' flag
 *     // Normally this is not needed, just use '$mdDialog.hide()' to close
 *     // the most recent dialog popup.
 *
 *     function closeAlert() {
 *       $mdDialog.hide( alert, "finished" );
 *       alert = undefined;
 *     }
 *
 *     // Dialog #2 - Demonstrate more complex dialogs construction and popup.
 *
 *     function showCustomGreeting($event) {
 *         $mdDialog.show({
 *           targetEvent: $event,
 *           template:
 *             '<md-dialog>' +
 *
 *             '  <md-dialog-content>Hello {{ employee }}!</md-dialog-content>' +
 *
 *             '  <md-dialog-actions>' +
 *             '    <md-button ng-click="closeDialog()" class="md-primary">' +
 *             '      Close Greeting' +
 *             '    </md-button>' +
 *             '  </md-dialog-actions>' +
 *             '</md-dialog>',
 *           controller: 'GreetingController',
 *           onComplete: afterShowAnimation,
 *           locals: { employee: $scope.userName }
 *         });
 *
 *         // When the 'enter' animation finishes...
 *
 *         function afterShowAnimation(scope, element, options) {
 *            // post-show code here: DOM element focus, etc.
 *         }
 *     }
 *
 *     // Dialog #3 - Demonstrate use of ControllerAs and passing $scope to dialog
 *     //             Here we used ng-controller="GreetingController as vm" and
 *     //             $scope.vm === <controller instance>
 *
 *     function showCustomGreeting() {
 *
 *        $mdDialog.show({
 *           clickOutsideToClose: true,
 *
 *           scope: $scope,        // use parent scope in template
 *           preserveScope: true,  // do not forget this if use parent scope

 *           // Since GreetingController is instantiated with ControllerAs syntax
 *           // AND we are passing the parent '$scope' to the dialog, we MUST
 *           // use 'vm.<xxx>' in the template markup
 *
 *           template: '<md-dialog>' +
 *                     '  <md-dialog-content>' +
 *                     '     Hi There {{vm.employee}}' +
 *                     '  </md-dialog-content>' +
 *                     '</md-dialog>',
 *
 *           controller: function DialogController($scope, $mdDialog) {
 *             $scope.closeDialog = function() {
 *               $mdDialog.hide();
 *             }
 *           }
 *        });
 *     }
 *
 *   }
 *
 *   // Greeting controller used with the more complex 'showCustomGreeting()' custom dialog
 *
 *   function GreetingController($scope, $mdDialog, employee) {
 *     // Assigned from construction <code>locals</code> options...
 *     $scope.employee = employee;
 *
 *     $scope.closeDialog = function() {
 *       // Easily hides most recent dialog shown...
 *       // no specific instance reference is needed.
 *       $mdDialog.hide();
 *     };
 *   }
 *
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdDialog#alert
 *
 * @description
 * Builds a preconfigured dialog with the specified message.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * - $mdDialogPreset#title(string) - Sets the alert title.
 * - $mdDialogPreset#textContent(string) - Sets the alert message.
 * - $mdDialogPreset#htmlContent(string) - Sets the alert message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#ok(string) - Sets the alert "Okay" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the alert dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#confirm
 *
 * @description
 * Builds a preconfigured dialog with the specified message. You can call show and the promise returned
 * will be resolved only if the user clicks the confirm action on the dialog.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * Additionally, it supports the following methods:
 *
 * - $mdDialogPreset#title(string) - Sets the confirm title.
 * - $mdDialogPreset#textContent(string) - Sets the confirm message.
 * - $mdDialogPreset#htmlContent(string) - Sets the confirm message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#ok(string) - Sets the confirm "Okay" button text.
 * - $mdDialogPreset#cancel(string) - Sets the confirm "Cancel" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the confirm dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#prompt
 *
 * @description
 * Builds a preconfigured dialog with the specified message and input box. You can call show and the promise returned
 * will be resolved only if the user clicks the prompt action on the dialog, passing the input value as the first argument.
 *
 * @returns {obj} an `$mdDialogPreset` with the chainable configuration methods:
 *
 * Additionally, it supports the following methods:
 *
 * - $mdDialogPreset#title(string) - Sets the prompt title.
 * - $mdDialogPreset#textContent(string) - Sets the prompt message.
 * - $mdDialogPreset#htmlContent(string) - Sets the prompt message as HTML. Requires ngSanitize
 *     module to be loaded. HTML is not run through Angular's compiler.
 * - $mdDialogPreset#placeholder(string) - Sets the placeholder text for the input.
 * - $mdDialogPreset#initialValue(string) - Sets the initial value for the prompt input.
 * - $mdDialogPreset#ok(string) - Sets the prompt "Okay" button text.
 * - $mdDialogPreset#cancel(string) - Sets the prompt "Cancel" button text.
 * - $mdDialogPreset#theme(string) - Sets the theme of the prompt dialog.
 * - $mdDialogPreset#targetEvent(DOMClickEvent=) - A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *
 */

/**
 * @ngdoc method
 * @name $mdDialog#show
 *
 * @description
 * Show a dialog with the specified options.
 *
 * @param {object} optionsOrPreset Either provide an `$mdDialogPreset` returned from `alert()`, and
 * `confirm()`, or an options object with the following properties:
 *   - `templateUrl` - `{string=}`: The url of a template that will be used as the content
 *   of the dialog.
 *   - `template` - `{string=}`: HTML template to show in the dialog. This **must** be trusted HTML
 *      with respect to Angular's [$sce service](https://docs.angularjs.org/api/ng/service/$sce).
 *      This template should **never** be constructed with any kind of user input or user data.
 *   - `contentElement` - `{string|Element}`: Instead of using a template, which will be compiled each time a
 *     dialog opens, you can also use a DOM element.<br/>
 *     * When specifying an element, which is present on the DOM, `$mdDialog` will temporary fetch the element into
 *       the dialog and restores it at the old DOM position upon close.
 *     * When specifying a string, the string be used as a CSS selector, to lookup for the element in the DOM.
 *   - `autoWrap` - `{boolean=}`: Whether or not to automatically wrap the template with a
 *     `<md-dialog>` tag if one is not provided. Defaults to true. Can be disabled if you provide a
 *     custom dialog directive.
 *   - `targetEvent` - `{DOMClickEvent=}`: A click's event object. When passed in as an option,
 *     the location of the click will be used as the starting point for the opening animation
 *     of the the dialog.
 *   - `openFrom` - `{string|Element|object}`: The query selector, DOM element or the Rect object
 *     that is used to determine the bounds (top, left, height, width) from which the Dialog will
 *     originate.
 *   - `closeTo` - `{string|Element|object}`: The query selector, DOM element or the Rect object
 *     that is used to determine the bounds (top, left, height, width) to which the Dialog will
 *     target.
 *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified,
 *     it will create a new isolate scope.
 *     This scope will be destroyed when the dialog is removed unless `preserveScope` is set to true.
 *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed. Default is false
 *   - `disableParentScroll` - `{boolean=}`: Whether to disable scrolling while the dialog is open.
 *     Default true.
 *   - `hasBackdrop` - `{boolean=}`: Whether there should be an opaque backdrop behind the dialog.
 *     Default true.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click outside the dialog to
 *     close it. Default false.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to close the dialog.
 *     Default true.
 *   - `focusOnOpen` - `{boolean=}`: An option to override focus behavior on open. Only disable if
 *     focusing some other way, as focus management is required for dialogs to be accessible.
 *     Defaults to true.
 *   - `controller` - `{function|string=}`: The controller to associate with the dialog. The controller
 *     will be injected with the local `$mdDialog`, which passes along a scope for the dialog.
 *   - `locals` - `{object=}`: An object containing key/value pairs. The keys will be used as names
 *     of values to inject into the controller. For example, `locals: {three: 3}` would inject
 *     `three` into the controller, with the value 3. If `bindToController` is true, they will be
 *     copied to the controller instead.
 *   - `bindToController` - `bool`: bind the locals to the controller, instead of passing them in.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
 *   - `parent` - `{element=}`: The element to append the dialog to. Defaults to appending
 *     to the root element of the application.
 *   - `onShowing` - `function(scope, element)`: Callback function used to announce the show() action is
 *     starting.
 *   - `onComplete` - `function(scope, element)`: Callback function used to announce when the show() action is
 *     finished.
 *   - `onRemoving` - `function(element, removePromise)`: Callback function used to announce the
 *      close/hide() action is starting. This allows developers to run custom animations
 *      in parallel the close animations.
 *   - `fullscreen` `{boolean=}`: An option to toggle whether the dialog should show in fullscreen
 *      or not. Defaults to `false`.
 *   - `multiple` `{boolean=}`: An option to allow this dialog to display over one that's currently open.
 * @returns {promise} A promise that can be resolved with `$mdDialog.hide()` or
 * rejected with `$mdDialog.cancel()`.
 */

/**
 * @ngdoc method
 * @name $mdDialog#hide
 *
 * @description
 * Hide an existing dialog and resolve the promise returned from `$mdDialog.show()`.
 *
 * @param {*=} response An argument for the resolved promise.
 *
 * @returns {promise} A promise that is resolved when the dialog has been closed.
 */

/**
 * @ngdoc method
 * @name $mdDialog#cancel
 *
 * @description
 * Hide an existing dialog and reject the promise returned from `$mdDialog.show()`.
 *
 * @param {*=} response An argument for the rejected promise.
 *
 * @returns {promise} A promise that is resolved when the dialog has been closed.
 */

function MdDialogProvider($$interimElementProvider) {
  // Elements to capture and redirect focus when the user presses tab at the dialog boundary.
  MdDialogController.$inject = ["$mdDialog", "$mdConstant"];
  dialogDefaultOptions.$inject = ["$mdDialog", "$mdAria", "$mdUtil", "$mdConstant", "$animate", "$document", "$window", "$rootElement", "$log", "$injector", "$mdTheming", "$interpolate", "$mdInteraction"];
  var topFocusTrap, bottomFocusTrap;

  return $$interimElementProvider('$mdDialog')
    .setDefaults({
      methods: ['disableParentScroll', 'hasBackdrop', 'clickOutsideToClose', 'escapeToClose',
          'targetEvent', 'closeTo', 'openFrom', 'parent', 'fullscreen', 'multiple'],
      options: dialogDefaultOptions
    })
    .addPreset('alert', {
      methods: ['title', 'htmlContent', 'textContent', 'content', 'ariaLabel', 'ok', 'theme',
          'css'],
      options: advancedDialogOptions
    })
    .addPreset('confirm', {
      methods: ['title', 'htmlContent', 'textContent', 'content', 'ariaLabel', 'ok', 'cancel',
          'theme', 'css'],
      options: advancedDialogOptions
    })
    .addPreset('prompt', {
      methods: ['title', 'htmlContent', 'textContent', 'initialValue', 'content', 'placeholder', 'ariaLabel',
          'ok', 'cancel', 'theme', 'css', 'required'],
      options: advancedDialogOptions
    });

  /* @ngInject */
  function advancedDialogOptions() {
    return {
      template: [
        '<md-dialog md-theme="{{ dialog.theme || dialog.defaultTheme }}" aria-label="{{ dialog.ariaLabel }}" ng-class="dialog.css">',
        '  <md-dialog-content class="md-dialog-content" role="document" tabIndex="-1">',
        '    <h2 class="md-title">{{ dialog.title }}</h2>',
        '    <div ng-if="::dialog.mdHtmlContent" class="md-dialog-content-body" ',
        '        ng-bind-html="::dialog.mdHtmlContent"></div>',
        '    <div ng-if="::!dialog.mdHtmlContent" class="md-dialog-content-body">',
        '      <p>{{::dialog.mdTextContent}}</p>',
        '    </div>',
        '    <md-input-container md-no-float ng-if="::dialog.$type == \'prompt\'" class="md-prompt-input-container">',
        '      <input ng-keypress="dialog.keypress($event)" md-autofocus ng-model="dialog.result" ' +
        '             placeholder="{{::dialog.placeholder}}" ng-required="dialog.required">',
        '    </md-input-container>',
        '  </md-dialog-content>',
        '  <md-dialog-actions>',
        '    <md-button ng-if="dialog.$type === \'confirm\' || dialog.$type === \'prompt\'"' +
        '               ng-click="dialog.abort()" class="md-primary md-cancel-button">',
        '      {{ dialog.cancel }}',
        '    </md-button>',
        '    <md-button ng-click="dialog.hide()" class="md-primary md-confirm-button" md-autofocus="dialog.$type===\'alert\'"' +
        '               ng-disabled="dialog.required && !dialog.result">',
        '      {{ dialog.ok }}',
        '    </md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
      ].join('').replace(/\s\s+/g, ''),
      controller: MdDialogController,
      controllerAs: 'dialog',
      bindToController: true,
    };
  }

  /**
   * Controller for the md-dialog interim elements
   * @ngInject
   */
  function MdDialogController($mdDialog, $mdConstant) {
    // For compatibility with AngularJS 1.6+, we should always use the $onInit hook in
    // interimElements. The $mdCompiler simulates the $onInit hook for all versions.
    this.$onInit = function() {
      var isPrompt = this.$type == 'prompt';

      if (isPrompt && this.initialValue) {
        this.result = this.initialValue;
      }

      this.hide = function() {
        $mdDialog.hide(isPrompt ? this.result : true);
      };
      this.abort = function() {
        $mdDialog.cancel();
      };
      this.keypress = function($event) {
        if ($event.keyCode === $mdConstant.KEY_CODE.ENTER) {
          $mdDialog.hide(this.result);
        }
      };
    };
  }

  /* @ngInject */
  function dialogDefaultOptions($mdDialog, $mdAria, $mdUtil, $mdConstant, $animate, $document, $window, $rootElement,
                                $log, $injector, $mdTheming, $interpolate, $mdInteraction) {

    return {
      hasBackdrop: true,
      isolateScope: true,
      onCompiling: beforeCompile,
      onShow: onShow,
      onShowing: beforeShow,
      onRemove: onRemove,
      clickOutsideToClose: false,
      escapeToClose: true,
      targetEvent: null,
      closeTo: null,
      openFrom: null,
      focusOnOpen: true,
      disableParentScroll: true,
      autoWrap: true,
      fullscreen: false,
      transformTemplate: function(template, options) {
        // Make the dialog container focusable, because otherwise the focus will be always redirected to
        // an element outside of the container, and the focus trap won't work probably..
        // Also the tabindex is needed for the `escapeToClose` functionality, because
        // the keyDown event can't be triggered when the focus is outside of the container.
        var startSymbol = $interpolate.startSymbol();
        var endSymbol = $interpolate.endSymbol();
        var theme = startSymbol + (options.themeWatch ? '' : '::') + 'theme' + endSymbol;
        return '<div class="md-dialog-container" tabindex="-1" md-theme="' + theme + '">' + validatedTemplate(template) + '</div>';

        /**
         * The specified template should contain a <md-dialog> wrapper element....
         */
        function validatedTemplate(template) {
          if (options.autoWrap && !/<\/md-dialog>/g.test(template)) {
            return '<md-dialog>' + (template || '') + '</md-dialog>';
          } else {
            return template || '';
          }
        }
      }
    };

    function beforeCompile(options) {
      // Automatically apply the theme, if the user didn't specify a theme explicitly.
      // Those option changes need to be done, before the compilation has started, because otherwise
      // the option changes will be not available in the $mdCompilers locales.
      options.defaultTheme = $mdTheming.defaultTheme();

      detectTheming(options);
    }

    function beforeShow(scope, element, options, controller) {

      if (controller) {
        var mdHtmlContent = controller.htmlContent || options.htmlContent || '';
        var mdTextContent = controller.textContent || options.textContent ||
            controller.content || options.content || '';

        if (mdHtmlContent && !$injector.has('$sanitize')) {
          throw Error('The ngSanitize module must be loaded in order to use htmlContent.');
        }

        if (mdHtmlContent && mdTextContent) {
          throw Error('md-dialog cannot have both `htmlContent` and `textContent`');
        }

        // Only assign the content if nothing throws, otherwise it'll still be compiled.
        controller.mdHtmlContent = mdHtmlContent;
        controller.mdTextContent = mdTextContent;
      }
    }

    /** Show method for dialogs */
    function onShow(scope, element, options, controller) {
      angular.element($document[0].body).addClass('md-dialog-is-showing');

      var dialogElement = element.find('md-dialog');

      // Once a dialog has `ng-cloak` applied on his template the dialog animation will not work properly.
      // This is a very common problem, so we have to notify the developer about this.
      if (dialogElement.hasClass('ng-cloak')) {
        var message = '$mdDialog: using `<md-dialog ng-cloak>` will affect the dialog opening animations.';
        $log.warn( message, element[0] );
      }

      captureParentAndFromToElements(options);
      configureAria(dialogElement, options);
      showBackdrop(scope, element, options);
      activateListeners(element, options);

      return dialogPopIn(element, options)
        .then(function() {
          lockScreenReader(element, options);
          warnDeprecatedActions();
          focusOnOpen();
        });

      /**
       * Check to see if they used the deprecated .md-actions class and log a warning
       */
      function warnDeprecatedActions() {
        if (element[0].querySelector('.md-actions')) {
          $log.warn('Using a class of md-actions is deprecated, please use <md-dialog-actions>.');
        }
      }

      /**
       * For alerts, focus on content... otherwise focus on
       * the close button (or equivalent)
       */
      function focusOnOpen() {
        if (options.focusOnOpen) {
          var target = $mdUtil.findFocusTarget(element) || findCloseButton() || dialogElement;
          target.focus();
        }

        /**
         * If no element with class dialog-close, try to find the last
         * button child in md-actions and assume it is a close button.
         *
         * If we find no actions at all, log a warning to the console.
         */
        function findCloseButton() {
          return element[0].querySelector('.dialog-close, md-dialog-actions button:last-child');
        }
      }
    }

    /**
     * Remove function for all dialogs
     */
    function onRemove(scope, element, options) {
      options.deactivateListeners();
      options.unlockScreenReader();
      options.hideBackdrop(options.$destroy);

      // Remove the focus traps that we added earlier for keeping focus within the dialog.
      if (topFocusTrap && topFocusTrap.parentNode) {
        topFocusTrap.parentNode.removeChild(topFocusTrap);
      }

      if (bottomFocusTrap && bottomFocusTrap.parentNode) {
        bottomFocusTrap.parentNode.removeChild(bottomFocusTrap);
      }

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal
      return !!options.$destroy ? detachAndClean() : animateRemoval().then( detachAndClean );

      /**
       * For normal closes, animate the removal.
       * For forced closes (like $destroy events), skip the animations
       */
      function animateRemoval() {
        return dialogPopOut(element, options);
      }

      /**
       * Detach the element
       */
      function detachAndClean() {
        angular.element($document[0].body).removeClass('md-dialog-is-showing');

        // Reverse the container stretch if using a content element.
        if (options.contentElement) {
          options.reverseContainerStretch();
        }

        // Exposed cleanup function from the $mdCompiler.
        options.cleanupElement();

        // Restores the focus to the origin element if the last interaction upon opening was a keyboard.
        if (!options.$destroy && options.originInteraction === 'keyboard') {
          options.origin.focus();
        }
      }
    }

    function detectTheming(options) {
      // Once the user specifies a targetEvent, we will automatically try to find the correct
      // nested theme.
      var targetEl;
      if (options.targetEvent && options.targetEvent.target) {
        targetEl = angular.element(options.targetEvent.target);
      }

      var themeCtrl = targetEl && targetEl.controller('mdTheme');

      if (!themeCtrl) {
        return;
      }

      options.themeWatch = themeCtrl.$shouldWatch;

      var theme = options.theme || themeCtrl.$mdTheme;

      if (theme) {
        options.scope.theme = theme;
      }

      var unwatch = themeCtrl.registerChanges(function (newTheme) {
        options.scope.theme = newTheme;

        if (!options.themeWatch) {
          unwatch();
        }
      });
    }

    /**
     * Capture originator/trigger/from/to element information (if available)
     * and the parent container for the dialog; defaults to the $rootElement
     * unless overridden in the options.parent
     */
    function captureParentAndFromToElements(options) {
          options.origin = angular.extend({
            element: null,
            bounds: null,
            focus: angular.noop
          }, options.origin || {});

          options.parent   = getDomElement(options.parent, $rootElement);
          options.closeTo  = getBoundingClientRect(getDomElement(options.closeTo));
          options.openFrom = getBoundingClientRect(getDomElement(options.openFrom));

          if ( options.targetEvent ) {
            options.origin = getBoundingClientRect(options.targetEvent.target, options.origin);
            options.originInteraction = $mdInteraction.getLastInteractionType();
          }


          /**
           * Identify the bounding RECT for the target element
           *
           */
          function getBoundingClientRect (element, orig) {
            var source = angular.element((element || {}));
            if (source && source.length) {
              // Compute and save the target element's bounding rect, so that if the
              // element is hidden when the dialog closes, we can shrink the dialog
              // back to the same position it expanded from.
              //
              // Checking if the source is a rect object or a DOM element
              var bounds = {top:0,left:0,height:0,width:0};
              var hasFn = angular.isFunction(source[0].getBoundingClientRect);

              return angular.extend(orig || {}, {
                  element : hasFn ? source : undefined,
                  bounds  : hasFn ? source[0].getBoundingClientRect() : angular.extend({}, bounds, source[0]),
                  focus   : angular.bind(source, source.focus),
              });
            }
          }

          /**
           * If the specifier is a simple string selector, then query for
           * the DOM element.
           */
          function getDomElement(element, defaultElement) {
            if (angular.isString(element)) {
              element = $document[0].querySelector(element);
            }

            // If we have a reference to a raw dom element, always wrap it in jqLite
            return angular.element(element || defaultElement);
          }

        }

    /**
     * Listen for escape keys and outside clicks to auto close
     */
    function activateListeners(element, options) {
      var window = angular.element($window);
      var onWindowResize = $mdUtil.debounce(function() {
        stretchDialogContainerToViewport(element, options);
      }, 60);

      var removeListeners = [];
      var smartClose = function() {
        // Only 'confirm' dialogs have a cancel button... escape/clickOutside will
        // cancel or fallback to hide.
        var closeFn = ( options.$type == 'alert' ) ? $mdDialog.hide : $mdDialog.cancel;
        $mdUtil.nextTick(closeFn, true);
      };

      if (options.escapeToClose) {
        var parentTarget = options.parent;
        var keyHandlerFn = function(ev) {
          if (ev.keyCode === $mdConstant.KEY_CODE.ESCAPE) {
            ev.stopPropagation();
            ev.preventDefault();

            smartClose();
          }
        };

        // Add keydown listeners
        element.on('keydown', keyHandlerFn);
        parentTarget.on('keydown', keyHandlerFn);

        // Queue remove listeners function
        removeListeners.push(function() {

          element.off('keydown', keyHandlerFn);
          parentTarget.off('keydown', keyHandlerFn);

        });
      }

      // Register listener to update dialog on window resize
      window.on('resize', onWindowResize);

      removeListeners.push(function() {
        window.off('resize', onWindowResize);
      });

      if (options.clickOutsideToClose) {
        var target = element;
        var sourceElem;

        // Keep track of the element on which the mouse originally went down
        // so that we can only close the backdrop when the 'click' started on it.
        // A simple 'click' handler does not work,
        // it sets the target object as the element the mouse went down on.
        var mousedownHandler = function(ev) {
          sourceElem = ev.target;
        };

        // We check if our original element and the target is the backdrop
        // because if the original was the backdrop and the target was inside the dialog
        // we don't want to dialog to close.
        var mouseupHandler = function(ev) {
          if (sourceElem === target[0] && ev.target === target[0]) {
            ev.stopPropagation();
            ev.preventDefault();

            smartClose();
          }
        };

        // Add listeners
        target.on('mousedown', mousedownHandler);
        target.on('mouseup', mouseupHandler);

        // Queue remove listeners function
        removeListeners.push(function() {
          target.off('mousedown', mousedownHandler);
          target.off('mouseup', mouseupHandler);
        });
      }

      // Attach specific `remove` listener handler
      options.deactivateListeners = function() {
        removeListeners.forEach(function(removeFn) {
          removeFn();
        });
        options.deactivateListeners = null;
      };
    }

    /**
     * Show modal backdrop element...
     */
    function showBackdrop(scope, element, options) {

      if (options.disableParentScroll) {
        // !! DO this before creating the backdrop; since disableScrollAround()
        //    configures the scroll offset; which is used by mdBackDrop postLink()
        options.restoreScroll = $mdUtil.disableScrollAround(element, options.parent);
      }

      if (options.hasBackdrop) {
        options.backdrop = $mdUtil.createBackdrop(scope, "md-dialog-backdrop md-opaque");
        $animate.enter(options.backdrop, options.parent);
      }

      /**
       * Hide modal backdrop element...
       */
      options.hideBackdrop = function hideBackdrop($destroy) {
        if (options.backdrop) {
          if ( !!$destroy ) options.backdrop.remove();
          else              $animate.leave(options.backdrop);
        }


        if (options.disableParentScroll) {
          options.restoreScroll && options.restoreScroll();
          delete options.restoreScroll;
        }

        options.hideBackdrop = null;
      };
    }

    /**
     * Inject ARIA-specific attributes appropriate for Dialogs
     */
    function configureAria(element, options) {

      var role = (options.$type === 'alert') ? 'alertdialog' : 'dialog';
      var dialogContent = element.find('md-dialog-content');
      var existingDialogId = element.attr('id');
      var dialogContentId = 'dialogContent_' + (existingDialogId || $mdUtil.nextUid());

      element.attr({
        'role': role,
        'tabIndex': '-1'
      });

      if (dialogContent.length === 0) {
        dialogContent = element;
        // If the dialog element already had an ID, don't clobber it.
        if (existingDialogId) {
          dialogContentId = existingDialogId;
        }
      }

      dialogContent.attr('id', dialogContentId);
      element.attr('aria-describedby', dialogContentId);

      if (options.ariaLabel) {
        $mdAria.expect(element, 'aria-label', options.ariaLabel);
      }
      else {
        $mdAria.expectAsync(element, 'aria-label', function() {
          // If dialog title is specified, set aria-label with it
          // See https://github.com/angular/material/issues/10582
          if (options.title) {
            return options.title;
          } else {
            var words = dialogContent.text().split(/\s+/);
            if (words.length > 3) words = words.slice(0, 3).concat('...');
            return words.join(' ');
          }
        });
      }

      // Set up elements before and after the dialog content to capture focus and
      // redirect back into the dialog.
      topFocusTrap = document.createElement('div');
      topFocusTrap.classList.add('md-dialog-focus-trap');
      topFocusTrap.tabIndex = 0;

      bottomFocusTrap = topFocusTrap.cloneNode(false);

      // When focus is about to move out of the dialog, we want to intercept it and redirect it
      // back to the dialog element.
      var focusHandler = function() {
        element.focus();
      };
      topFocusTrap.addEventListener('focus', focusHandler);
      bottomFocusTrap.addEventListener('focus', focusHandler);

      // The top focus trap inserted immeidately before the md-dialog element (as a sibling).
      // The bottom focus trap is inserted at the very end of the md-dialog element (as a child).
      element[0].parentNode.insertBefore(topFocusTrap, element[0]);
      element.after(bottomFocusTrap);
    }

    /**
     * Prevents screen reader interaction behind modal window
     * on swipe interfaces
     */
    function lockScreenReader(element, options) {
      var isHidden = true;

      // get raw DOM node
      walkDOM(element[0]);

      options.unlockScreenReader = function() {
        isHidden = false;
        walkDOM(element[0]);

        options.unlockScreenReader = null;
      };

      /**
       * Walk DOM to apply or remove aria-hidden on sibling nodes
       * and parent sibling nodes
       *
       */
      function walkDOM(element) {
        while (element.parentNode) {
          if (element === document.body) {
            return;
          }
          var children = element.parentNode.children;
          for (var i = 0; i < children.length; i++) {
            // skip over child if it is an ascendant of the dialog
            // or a script or style tag
            if (element !== children[i] &&
             !isNodeOneOf(children[i], ['SCRIPT', 'STYLE']) &&
             !children[i].hasAttribute('aria-live')) {
              children[i].setAttribute('aria-hidden', isHidden);
            }
          }

          walkDOM(element = element.parentNode);
        }
      }
    }

    /**
     * Ensure the dialog container fill-stretches to the viewport
     */
    function stretchDialogContainerToViewport(container, options) {
      var isFixed = $window.getComputedStyle($document[0].body).position == 'fixed';
      var backdrop = options.backdrop ? $window.getComputedStyle(options.backdrop[0]) : null;
      var height = backdrop ? Math.min($document[0].body.clientHeight, Math.ceil(Math.abs(parseInt(backdrop.height, 10)))) : 0;

      var previousStyles = {
        top: container.css('top'),
        height: container.css('height')
      };

      // If the body is fixed, determine the distance to the viewport in relative from the parent.
      var parentTop = Math.abs(options.parent[0].getBoundingClientRect().top);

      container.css({
        top: (isFixed ? parentTop : 0) + 'px',
        height: height ? height + 'px' : '100%'
      });

      return function() {
        // Reverts the modified styles back to the previous values.
        // This is needed for contentElements, which should have the same styles after close
        // as before.
        container.css(previousStyles);
      };
    }

    /**
     *  Dialog open and pop-in animation
     */
    function dialogPopIn(container, options) {
      // Add the `md-dialog-container` to the DOM
      options.parent.append(container);
      options.reverseContainerStretch = stretchDialogContainerToViewport(container, options);

      var dialogEl = container.find('md-dialog');
      var animator = $mdUtil.dom.animator;
      var buildTranslateToOrigin = animator.calculateZoomToOrigin;
      var translateOptions = {transitionInClass: 'md-transition-in', transitionOutClass: 'md-transition-out'};
      var from = animator.toTransformCss(buildTranslateToOrigin(dialogEl, options.openFrom || options.origin));
      var to = animator.toTransformCss("");  // defaults to center display (or parent or $rootElement)

      dialogEl.toggleClass('md-dialog-fullscreen', !!options.fullscreen);

      return animator
        .translate3d(dialogEl, from, to, translateOptions)
        .then(function(animateReversal) {

          // Build a reversal translate function synced to this translation...
          options.reverseAnimate = function() {
            delete options.reverseAnimate;

            if (options.closeTo) {
              // Using the opposite classes to create a close animation to the closeTo element
              translateOptions = {transitionInClass: 'md-transition-out', transitionOutClass: 'md-transition-in'};
              from = to;
              to = animator.toTransformCss(buildTranslateToOrigin(dialogEl, options.closeTo));

              return animator
                .translate3d(dialogEl, from, to,translateOptions);
            }

            return animateReversal(
              to = animator.toTransformCss(
                // in case the origin element has moved or is hidden,
                // let's recalculate the translateCSS
                buildTranslateToOrigin(dialogEl, options.origin)
              )
            );

          };

          // Function to revert the generated animation styles on the dialog element.
          // Useful when using a contentElement instead of a template.
          options.clearAnimate = function() {
            delete options.clearAnimate;

            // Remove the transition classes, added from $animateCSS, since those can't be removed
            // by reversely running the animator.
            dialogEl.removeClass([
              translateOptions.transitionOutClass,
              translateOptions.transitionInClass
            ].join(' '));

            // Run the animation reversely to remove the previous added animation styles.
            return animator.translate3d(dialogEl, to, animator.toTransformCss(''), {});
          };

          return true;
        });
    }

    /**
     * Dialog close and pop-out animation
     */
    function dialogPopOut(container, options) {
      return options.reverseAnimate().then(function() {
        if (options.contentElement) {
          // When we use a contentElement, we want the element to be the same as before.
          // That means, that we have to clear all the animation properties, like transform.
          options.clearAnimate();
        }
      });
    }

    /**
     * Utility function to filter out raw DOM nodes
     */
    function isNodeOneOf(elem, nodeTypeArray) {
      if (nodeTypeArray.indexOf(elem.nodeName) !== -1) {
        return true;
      }
    }

  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.divider
 * @description Divider module!
 */
MdDividerDirective.$inject = ["$mdTheming"];
angular.module('material.components.divider', [
  'material.core'
])
  .directive('mdDivider', MdDividerDirective);

/**
 * @ngdoc directive
 * @name mdDivider
 * @module material.components.divider
 * @restrict E
 *
 * @description
 * Dividers group and separate content within lists and page layouts using strong visual and spatial distinctions. This divider is a thin rule, lightweight enough to not distract the user from content.
 *
 * @param {boolean=} md-inset Add this attribute to activate the inset divider style.
 * @usage
 * <hljs lang="html">
 * <md-divider></md-divider>
 *
 * <md-divider md-inset></md-divider>
 * </hljs>
 *
 */
function MdDividerDirective($mdTheming) {
  return {
    restrict: 'E',
    link: $mdTheming
  };
}

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc module
   * @name material.components.fabActions
   */
  MdFabActionsDirective.$inject = ["$mdUtil"];
  angular
    .module('material.components.fabActions', ['material.core'])
    .directive('mdFabActions', MdFabActionsDirective);

  /**
   * @ngdoc directive
   * @name mdFabActions
   * @module material.components.fabActions
   *
   * @restrict E
   *
   * @description
   * The `<md-fab-actions>` directive is used inside of a `<md-fab-speed-dial>` or
   * `<md-fab-toolbar>` directive to mark an element (or elements) as the actions and setup the
   * proper event listeners.
   *
   * @usage
   * See the `<md-fab-speed-dial>` or `<md-fab-toolbar>` directives for example usage.
   */
  function MdFabActionsDirective($mdUtil) {
    return {
      restrict: 'E',

      require: ['^?mdFabSpeedDial', '^?mdFabToolbar'],

      compile: function(element, attributes) {
        var children = element.children();

        var hasNgRepeat = $mdUtil.prefixer().hasAttribute(children, 'ng-repeat');

        // Support both ng-repeat and static content
        if (hasNgRepeat) {
          children.addClass('md-fab-action-item');
        } else {
          // Wrap every child in a new div and add a class that we can scale/fling independently
          children.wrap('<div class="md-fab-action-item">');
        }
      }
    };
  }

})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  MdFabController.$inject = ["$scope", "$element", "$animate", "$mdUtil", "$mdConstant", "$timeout"];
  angular.module('material.components.fabShared', ['material.core'])
    .controller('MdFabController', MdFabController);

  function MdFabController($scope, $element, $animate, $mdUtil, $mdConstant, $timeout) {
    var vm = this;
    var initialAnimationAttempts = 0;

    // NOTE: We use async eval(s) below to avoid conflicts with any existing digest loops

    vm.open = function() {
      $scope.$evalAsync("vm.isOpen = true");
    };

    vm.close = function() {
      // Async eval to avoid conflicts with existing digest loops
      $scope.$evalAsync("vm.isOpen = false");

      // Focus the trigger when the element closes so users can still tab to the next item
      $element.find('md-fab-trigger')[0].focus();
    };

    // Toggle the open/close state when the trigger is clicked
    vm.toggle = function() {
      $scope.$evalAsync("vm.isOpen = !vm.isOpen");
    };

    /*
     * AngularJS Lifecycle hook for newer AngularJS versions.
     * Bindings are not guaranteed to have been assigned in the controller, but they are in the $onInit hook.
     */
    vm.$onInit = function() {
      setupDefaults();
      setupListeners();
      setupWatchers();

      fireInitialAnimations();
    };

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
    // manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }

    function setupDefaults() {
      // Set the default direction to 'down' if none is specified
      vm.direction = vm.direction || 'down';

      // Set the default to be closed
      vm.isOpen = vm.isOpen || false;

      // Start the keyboard interaction at the first action
      resetActionIndex();

      // Add an animations waiting class so we know not to run
      $element.addClass('md-animations-waiting');
    }

    function setupListeners() {
      var eventTypes = [
        'click', 'focusin', 'focusout'
      ];

      // Add our listeners
      angular.forEach(eventTypes, function(eventType) {
        $element.on(eventType, parseEvents);
      });

      // Remove our listeners when destroyed
      $scope.$on('$destroy', function() {
        angular.forEach(eventTypes, function(eventType) {
          $element.off(eventType, parseEvents);
        });

        // remove any attached keyboard handlers in case element is removed while
        // speed dial is open
        disableKeyboard();
      });
    }

    var closeTimeout;
    function parseEvents(event) {
      // If the event is a click, just handle it
      if (event.type == 'click') {
        handleItemClick(event);
      }

      // If we focusout, set a timeout to close the element
      if (event.type == 'focusout' && !closeTimeout) {
        closeTimeout = $timeout(function() {
          vm.close();
        }, 100, false);
      }

      // If we see a focusin and there is a timeout about to run, cancel it so we stay open
      if (event.type == 'focusin' && closeTimeout) {
        $timeout.cancel(closeTimeout);
        closeTimeout = null;
      }
    }

    function resetActionIndex() {
      vm.currentActionIndex = -1;
    }

    function setupWatchers() {
      // Watch for changes to the direction and update classes/attributes
      $scope.$watch('vm.direction', function(newDir, oldDir) {
        // Add the appropriate classes so we can target the direction in the CSS
        $animate.removeClass($element, 'md-' + oldDir);
        $animate.addClass($element, 'md-' + newDir);

        // Reset the action index since it may have changed
        resetActionIndex();
      });

      var trigger, actions;

      // Watch for changes to md-open
      $scope.$watch('vm.isOpen', function(isOpen) {
        // Reset the action index since it may have changed
        resetActionIndex();

        // We can't get the trigger/actions outside of the watch because the component hasn't been
        // linked yet, so we wait until the first watch fires to cache them.
        if (!trigger || !actions) {
          trigger = getTriggerElement();
          actions = getActionsElement();
        }

        if (isOpen) {
          enableKeyboard();
        } else {
          disableKeyboard();
        }

        var toAdd = isOpen ? 'md-is-open' : '';
        var toRemove = isOpen ? '' : 'md-is-open';

        // Set the proper ARIA attributes
        trigger.attr('aria-haspopup', true);
        trigger.attr('aria-expanded', isOpen);
        actions.attr('aria-hidden', !isOpen);

        // Animate the CSS classes
        $animate.setClass($element, toAdd, toRemove);
      });
    }

    function fireInitialAnimations() {
      // If the element is actually visible on the screen
      if ($element[0].scrollHeight > 0) {
        // Fire our animation
        $animate.addClass($element, '_md-animations-ready').then(function() {
          // Remove the waiting class
          $element.removeClass('md-animations-waiting');
        });
      }

      // Otherwise, try for up to 1 second before giving up
      else if (initialAnimationAttempts < 10) {
        $timeout(fireInitialAnimations, 100);

        // Increment our counter
        initialAnimationAttempts = initialAnimationAttempts + 1;
      }
    }

    function enableKeyboard() {
      $element.on('keydown', keyPressed);

      // On the next tick, setup a check for outside clicks; we do this on the next tick to avoid
      // clicks/touches that result in the isOpen attribute changing (e.g. a bound radio button)
      $mdUtil.nextTick(function() {
        angular.element(document).on('click touchend', checkForOutsideClick);
      });

      // TODO: On desktop, we should be able to reset the indexes so you cannot tab through, but
      // this breaks accessibility, especially on mobile, since you have no arrow keys to press
      //resetActionTabIndexes();
    }

    function disableKeyboard() {
      $element.off('keydown', keyPressed);
      angular.element(document).off('click touchend', checkForOutsideClick);
    }

    function checkForOutsideClick(event) {
      if (event.target) {
        var closestTrigger = $mdUtil.getClosest(event.target, 'md-fab-trigger');
        var closestActions = $mdUtil.getClosest(event.target, 'md-fab-actions');

        if (!closestTrigger && !closestActions) {
          vm.close();
        }
      }
    }

    function keyPressed(event) {
      switch (event.which) {
        case $mdConstant.KEY_CODE.ESCAPE: vm.close(); event.preventDefault(); return false;
        case $mdConstant.KEY_CODE.LEFT_ARROW: doKeyLeft(event); return false;
        case $mdConstant.KEY_CODE.UP_ARROW: doKeyUp(event); return false;
        case $mdConstant.KEY_CODE.RIGHT_ARROW: doKeyRight(event); return false;
        case $mdConstant.KEY_CODE.DOWN_ARROW: doKeyDown(event); return false;
      }
    }

    function doActionPrev(event) {
      focusAction(event, -1);
    }

    function doActionNext(event) {
      focusAction(event, 1);
    }

    function focusAction(event, direction) {
      var actions = resetActionTabIndexes();

      // Increment/decrement the counter with restrictions
      vm.currentActionIndex = vm.currentActionIndex + direction;
      vm.currentActionIndex = Math.min(actions.length - 1, vm.currentActionIndex);
      vm.currentActionIndex = Math.max(0, vm.currentActionIndex);

      // Focus the element
      var focusElement =  angular.element(actions[vm.currentActionIndex]).children()[0];
      angular.element(focusElement).attr('tabindex', 0);
      focusElement.focus();

      // Make sure the event doesn't bubble and cause something else
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    function resetActionTabIndexes() {
      // Grab all of the actions
      var actions = getActionsElement()[0].querySelectorAll('.md-fab-action-item');

      // Disable all other actions for tabbing
      angular.forEach(actions, function(action) {
        angular.element(angular.element(action).children()[0]).attr('tabindex', -1);
      });

      return actions;
    }

    function doKeyLeft(event) {
      if (vm.direction === 'left') {
        doActionNext(event);
      } else {
        doActionPrev(event);
      }
    }

    function doKeyUp(event) {
      if (vm.direction === 'down') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function doKeyRight(event) {
      if (vm.direction === 'left') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function doKeyDown(event) {
      if (vm.direction === 'up') {
        doActionPrev(event);
      } else {
        doActionNext(event);
      }
    }

    function isTrigger(element) {
      return $mdUtil.getClosest(element, 'md-fab-trigger');
    }

    function isAction(element) {
      return $mdUtil.getClosest(element, 'md-fab-actions');
    }

    function handleItemClick(event) {
      if (isTrigger(event.target)) {
        vm.toggle();
      }

      if (isAction(event.target)) {
        vm.close();
      }
    }

    function getTriggerElement() {
      return $element.find('md-fab-trigger');
    }

    function getActionsElement() {
      return $element.find('md-fab-actions');
    }
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * The duration of the CSS animation in milliseconds.
   *
   * @type {number}
   */
  MdFabSpeedDialFlingAnimation.$inject = ["$timeout"];
  MdFabSpeedDialScaleAnimation.$inject = ["$timeout"];
  var cssAnimationDuration = 300;

  /**
   * @ngdoc module
   * @name material.components.fabSpeedDial
   */
  angular
    // Declare our module
    .module('material.components.fabSpeedDial', [
      'material.core',
      'material.components.fabShared',
      'material.components.fabActions'
    ])

    // Register our directive
    .directive('mdFabSpeedDial', MdFabSpeedDialDirective)

    // Register our custom animations
    .animation('.md-fling', MdFabSpeedDialFlingAnimation)
    .animation('.md-scale', MdFabSpeedDialScaleAnimation)

    // Register a service for each animation so that we can easily inject them into unit tests
    .service('mdFabSpeedDialFlingAnimation', MdFabSpeedDialFlingAnimation)
    .service('mdFabSpeedDialScaleAnimation', MdFabSpeedDialScaleAnimation);

  /**
   * @ngdoc directive
   * @name mdFabSpeedDial
   * @module material.components.fabSpeedDial
   *
   * @restrict E
   *
   * @description
   * The `<md-fab-speed-dial>` directive is used to present a series of popup elements (usually
   * `<md-button>`s) for quick access to common actions.
   *
   * There are currently two animations available by applying one of the following classes to
   * the component:
   *
   *  - `md-fling` - The speed dial items appear from underneath the trigger and move into their
   *    appropriate positions.
   *  - `md-scale` - The speed dial items appear in their proper places by scaling from 0% to 100%.
   *
   * You may also easily position the trigger by applying one one of the following classes to the
   * `<md-fab-speed-dial>` element:
   *  - `md-fab-top-left`
   *  - `md-fab-top-right`
   *  - `md-fab-bottom-left`
   *  - `md-fab-bottom-right`
   *
   * These CSS classes use `position: absolute`, so you need to ensure that the container element
   * also uses `position: absolute` or `position: relative` in order for them to work.
   *
   * Additionally, you may use the standard `ng-mouseenter` and `ng-mouseleave` directives to
   * open or close the speed dial. However, if you wish to allow users to hover over the empty
   * space where the actions will appear, you must also add the `md-hover-full` class to the speed
   * dial element. Without this, the hover effect will only occur on top of the trigger.
   *
   * See the demos for more information.
   *
   * ## Troubleshooting
   *
   * If your speed dial shows the closing animation upon launch, you may need to use `ng-cloak` on
   * the parent container to ensure that it is only visible once ready. We have plans to remove this
   * necessity in the future.
   *
   * @usage
   * <hljs lang="html">
   * <md-fab-speed-dial md-direction="up" class="md-fling">
   *   <md-fab-trigger>
   *     <md-button aria-label="Add..."><md-icon md-svg-src="/img/icons/plus.svg"></md-icon></md-button>
   *   </md-fab-trigger>
   *
   *   <md-fab-actions>
   *     <md-button aria-label="Add User">
   *       <md-icon md-svg-src="/img/icons/user.svg"></md-icon>
   *     </md-button>
   *
   *     <md-button aria-label="Add Group">
   *       <md-icon md-svg-src="/img/icons/group.svg"></md-icon>
   *     </md-button>
   *   </md-fab-actions>
   * </md-fab-speed-dial>
   * </hljs>
   *
   * @param {string} md-direction From which direction you would like the speed dial to appear
   * relative to the trigger element.
   * @param {expression=} md-open Programmatically control whether or not the speed-dial is visible.
   */
  function MdFabSpeedDialDirective() {
    return {
      restrict: 'E',

      scope: {
        direction: '@?mdDirection',
        isOpen: '=?mdOpen'
      },

      bindToController: true,
      controller: 'MdFabController',
      controllerAs: 'vm',

      link: FabSpeedDialLink
    };

    function FabSpeedDialLink(scope, element) {
      // Prepend an element to hold our CSS variables so we can use them in the animations below
      element.prepend('<div class="_md-css-variables"></div>');
    }
  }

  function MdFabSpeedDialFlingAnimation($timeout) {
    function delayDone(done) { $timeout(done, cssAnimationDuration, false); }

    function runAnimation(element) {
      // Don't run if we are still waiting and we are not ready
      if (element.hasClass('md-animations-waiting') && !element.hasClass('_md-animations-ready')) {
        return;
      }

      var el = element[0];
      var ctrl = element.controller('mdFabSpeedDial');
      var items = el.querySelectorAll('.md-fab-action-item');

      // Grab our trigger element
      var triggerElement = el.querySelector('md-fab-trigger');

      // Grab our element which stores CSS variables
      var variablesElement = el.querySelector('._md-css-variables');

      // Setup JS variables based on our CSS variables
      var startZIndex = parseInt(window.getComputedStyle(variablesElement).zIndex);

      // Always reset the items to their natural position/state
      angular.forEach(items, function(item, index) {
        var styles = item.style;

        styles.transform = styles.webkitTransform = '';
        styles.transitionDelay = '';
        styles.opacity = 1;

        // Make the items closest to the trigger have the highest z-index
        styles.zIndex = (items.length - index) + startZIndex;
      });

      // Set the trigger to be above all of the actions so they disappear behind it.
      triggerElement.style.zIndex = startZIndex + items.length + 1;

      // If the control is closed, hide the items behind the trigger
      if (!ctrl.isOpen) {
        angular.forEach(items, function(item, index) {
          var newPosition, axis;
          var styles = item.style;

          // Make sure to account for differences in the dimensions of the trigger verses the items
          // so that we can properly center everything; this helps hide the item's shadows behind
          // the trigger.
          var triggerItemHeightOffset = (triggerElement.clientHeight - item.clientHeight) / 2;
          var triggerItemWidthOffset = (triggerElement.clientWidth - item.clientWidth) / 2;

          switch (ctrl.direction) {
            case 'up':
              newPosition = (item.scrollHeight * (index + 1) + triggerItemHeightOffset);
              axis = 'Y';
              break;
            case 'down':
              newPosition = -(item.scrollHeight * (index + 1) + triggerItemHeightOffset);
              axis = 'Y';
              break;
            case 'left':
              newPosition = (item.scrollWidth * (index + 1) + triggerItemWidthOffset);
              axis = 'X';
              break;
            case 'right':
              newPosition = -(item.scrollWidth * (index + 1) + triggerItemWidthOffset);
              axis = 'X';
              break;
          }

          var newTranslate = 'translate' + axis + '(' + newPosition + 'px)';

          styles.transform = styles.webkitTransform = newTranslate;
        });
      }
    }

    return {
      addClass: function(element, className, done) {
        if (element.hasClass('md-fling')) {
          runAnimation(element);
          delayDone(done);
        } else {
          done();
        }
      },
      removeClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      }
    };
  }

  function MdFabSpeedDialScaleAnimation($timeout) {
    function delayDone(done) { $timeout(done, cssAnimationDuration, false); }

    var delay = 65;

    function runAnimation(element) {
      var el = element[0];
      var ctrl = element.controller('mdFabSpeedDial');
      var items = el.querySelectorAll('.md-fab-action-item');

      // Grab our element which stores CSS variables
      var variablesElement = el.querySelector('._md-css-variables');

      // Setup JS variables based on our CSS variables
      var startZIndex = parseInt(window.getComputedStyle(variablesElement).zIndex);

      // Always reset the items to their natural position/state
      angular.forEach(items, function(item, index) {
        var styles = item.style,
          offsetDelay = index * delay;

        styles.opacity = ctrl.isOpen ? 1 : 0;
        styles.transform = styles.webkitTransform = ctrl.isOpen ? 'scale(1)' : 'scale(0)';
        styles.transitionDelay = (ctrl.isOpen ? offsetDelay : (items.length - offsetDelay)) + 'ms';

        // Make the items closest to the trigger have the highest z-index
        styles.zIndex = (items.length - index) + startZIndex;
      });
    }

    return {
      addClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      },

      removeClass: function(element, className, done) {
        runAnimation(element);
        delayDone(done);
      }
    };
  }
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc module
   * @name material.components.fabToolbar
   */
  angular
    // Declare our module
    .module('material.components.fabToolbar', [
      'material.core',
      'material.components.fabShared',
      'material.components.fabActions'
    ])

    // Register our directive
    .directive('mdFabToolbar', MdFabToolbarDirective)

    // Register our custom animations
    .animation('.md-fab-toolbar', MdFabToolbarAnimation)

    // Register a service for the animation so that we can easily inject it into unit tests
    .service('mdFabToolbarAnimation', MdFabToolbarAnimation);

  /**
   * @ngdoc directive
   * @name mdFabToolbar
   * @module material.components.fabToolbar
   *
   * @restrict E
   *
   * @description
   *
   * The `<md-fab-toolbar>` directive is used to present a toolbar of elements (usually `<md-button>`s)
   * for quick access to common actions when a floating action button is activated (via click or
   * keyboard navigation).
   *
   * You may also easily position the trigger by applying one one of the following classes to the
   * `<md-fab-toolbar>` element:
   *  - `md-fab-top-left`
   *  - `md-fab-top-right`
   *  - `md-fab-bottom-left`
   *  - `md-fab-bottom-right`
   *
   * These CSS classes use `position: absolute`, so you need to ensure that the container element
   * also uses `position: absolute` or `position: relative` in order for them to work.
   *
   * @usage
   *
   * <hljs lang="html">
   * <md-fab-toolbar md-direction='left'>
   *   <md-fab-trigger>
   *     <md-button aria-label="Add..."><md-icon md-svg-src="/img/icons/plus.svg"></md-icon></md-button>
   *   </md-fab-trigger>
   *
   *   <md-toolbar>
   *    <md-fab-actions>
   *      <md-button aria-label="Add User">
   *        <md-icon md-svg-src="/img/icons/user.svg"></md-icon>
   *      </md-button>
   *
   *      <md-button aria-label="Add Group">
   *        <md-icon md-svg-src="/img/icons/group.svg"></md-icon>
   *      </md-button>
   *    </md-fab-actions>
   *   </md-toolbar>
   * </md-fab-toolbar>
   * </hljs>
   *
   * @param {string} md-direction From which direction you would like the toolbar items to appear
   * relative to the trigger element. Supports `left` and `right` directions.
   * @param {expression=} md-open Programmatically control whether or not the toolbar is visible.
   */
  function MdFabToolbarDirective() {
    return {
      restrict: 'E',
      transclude: true,
      template: '<div class="md-fab-toolbar-wrapper">' +
      '  <div class="md-fab-toolbar-content" ng-transclude></div>' +
      '</div>',

      scope: {
        direction: '@?mdDirection',
        isOpen: '=?mdOpen'
      },

      bindToController: true,
      controller: 'MdFabController',
      controllerAs: 'vm',

      link: link
    };

    function link(scope, element, attributes) {
      // Add the base class for animations
      element.addClass('md-fab-toolbar');

      // Prepend the background element to the trigger's button
      element.find('md-fab-trigger').find('button')
        .prepend('<div class="md-fab-toolbar-background"></div>');
    }
  }

  function MdFabToolbarAnimation() {

    function runAnimation(element, className, done) {
      // If no className was specified, don't do anything
      if (!className) {
        return;
      }

      var el = element[0];
      var ctrl = element.controller('mdFabToolbar');

      // Grab the relevant child elements
      var backgroundElement = el.querySelector('.md-fab-toolbar-background');
      var triggerElement = el.querySelector('md-fab-trigger button');
      var toolbarElement = el.querySelector('md-toolbar');
      var iconElement = el.querySelector('md-fab-trigger button md-icon');
      var actions = element.find('md-fab-actions').children();

      // If we have both elements, use them to position the new background
      if (triggerElement && backgroundElement) {
        // Get our variables
        var color = window.getComputedStyle(triggerElement).getPropertyValue('background-color');
        var width = el.offsetWidth;
        var height = el.offsetHeight;

        // Make it twice as big as it should be since we scale from the center
        var scale = 2 * (width / triggerElement.offsetWidth);

        // Set some basic styles no matter what animation we're doing
        backgroundElement.style.backgroundColor = color;
        backgroundElement.style.borderRadius = width + 'px';

        // If we're open
        if (ctrl.isOpen) {
          // Turn on toolbar pointer events when closed
          toolbarElement.style.pointerEvents = 'inherit';

          backgroundElement.style.width = triggerElement.offsetWidth + 'px';
          backgroundElement.style.height = triggerElement.offsetHeight + 'px';
          backgroundElement.style.transform = 'scale(' + scale + ')';

          // Set the next close animation to have the proper delays
          backgroundElement.style.transitionDelay = '0ms';
          iconElement && (iconElement.style.transitionDelay = '.3s');

          // Apply a transition delay to actions
          angular.forEach(actions, function(action, index) {
            action.style.transitionDelay = (actions.length - index) * 25 + 'ms';
          });
        } else {
          // Turn off toolbar pointer events when closed
          toolbarElement.style.pointerEvents = 'none';

          // Scale it back down to the trigger's size
          backgroundElement.style.transform = 'scale(1)';

          // Reset the position
          backgroundElement.style.top = '0';

          if (element.hasClass('md-right')) {
            backgroundElement.style.left = '0';
            backgroundElement.style.right = null;
          }

          if (element.hasClass('md-left')) {
            backgroundElement.style.right = '0';
            backgroundElement.style.left = null;
          }

          // Set the next open animation to have the proper delays
          backgroundElement.style.transitionDelay = '200ms';
          iconElement && (iconElement.style.transitionDelay = '0ms');

          // Apply a transition delay to actions
          angular.forEach(actions, function(action, index) {
            action.style.transitionDelay = 200 + (index * 25) + 'ms';
          });
        }
      }
    }

    return {
      addClass: function(element, className, done) {
        runAnimation(element, className, done);
        done();
      },

      removeClass: function(element, className, done) {
        runAnimation(element, className, done);
        done();
      }
    };
  }
})();

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.icon
 * @description
 * Icon
 */
angular.module('material.components.icon', ['material.core']);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.gridList
 */
GridListController.$inject = ["$mdUtil"];
GridLayoutFactory.$inject = ["$mdUtil"];
GridListDirective.$inject = ["$interpolate", "$mdConstant", "$mdGridLayout", "$mdMedia"];
GridTileDirective.$inject = ["$mdMedia"];
angular.module('material.components.gridList', ['material.core'])
       .directive('mdGridList', GridListDirective)
       .directive('mdGridTile', GridTileDirective)
       .directive('mdGridTileFooter', GridTileCaptionDirective)
       .directive('mdGridTileHeader', GridTileCaptionDirective)
       .factory('$mdGridLayout', GridLayoutFactory);

/**
 * @ngdoc directive
 * @name mdGridList
 * @module material.components.gridList
 * @restrict E
 * @description
 * Grid lists are an alternative to standard list views. Grid lists are distinct
 * from grids used for layouts and other visual presentations.
 *
 * A grid list is best suited to presenting a homogenous data type, typically
 * images, and is optimized for visual comprehension and differentiating between
 * like data types.
 *
 * A grid list is a continuous element consisting of tessellated, regular
 * subdivisions called cells that contain tiles (`md-grid-tile`).
 *
 * <img src="//material-design.storage.googleapis.com/publish/v_2/material_ext_publish/0Bx4BSt6jniD7OVlEaXZ5YmU1Xzg/components_grids_usage2.png"
 *    style="width: 300px; height: auto; margin-right: 16px;" alt="Concept of grid explained visually">
 * <img src="//material-design.storage.googleapis.com/publish/v_2/material_ext_publish/0Bx4BSt6jniD7VGhsOE5idWlJWXM/components_grids_usage3.png"
 *    style="width: 300px; height: auto;" alt="Grid concepts legend">
 *
 * Cells are arrayed vertically and horizontally within the grid.
 *
 * Tiles hold content and can span one or more cells vertically or horizontally.
 *
 * ### Responsive Attributes
 *
 * The `md-grid-list` directive supports "responsive" attributes, which allow
 * different `md-cols`, `md-gutter` and `md-row-height` values depending on the
 * currently matching media query.
 *
 * In order to set a responsive attribute, first define the fallback value with
 * the standard attribute name, then add additional attributes with the
 * following convention: `{base-attribute-name}-{media-query-name}="{value}"`
 * (ie. `md-cols-lg="8"`)
 *
 * @param {number} md-cols Number of columns in the grid.
 * @param {string} md-row-height One of
 * <ul>
 *   <li>CSS length - Fixed height rows (eg. `8px` or `1rem`)</li>
 *   <li>`{width}:{height}` - Ratio of width to height (eg.
 *   `md-row-height="16:9"`)</li>
 *   <li>`"fit"` - Height will be determined by subdividing the available
 *   height by the number of rows</li>
 * </ul>
 * @param {string=} md-gutter The amount of space between tiles in CSS units
 *     (default 1px)
 * @param {expression=} md-on-layout Expression to evaluate after layout. Event
 *     object is available as `$event`, and contains performance information.
 *
 * @usage
 * Basic:
 * <hljs lang="html">
 * <md-grid-list md-cols="5" md-gutter="1em" md-row-height="4:3">
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Fixed-height rows:
 * <hljs lang="html">
 * <md-grid-list md-cols="4" md-row-height="200px" ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Fit rows:
 * <hljs lang="html">
 * <md-grid-list md-cols="4" md-row-height="fit" style="height: 400px;" ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 *
 * Using responsive attributes:
 * <hljs lang="html">
 * <md-grid-list
 *     md-cols-sm="2"
 *     md-cols-md="4"
 *     md-cols-lg="8"
 *     md-cols-gt-lg="12"
 *     ...>
 *   <md-grid-tile></md-grid-tile>
 * </md-grid-list>
 * </hljs>
 */
function GridListDirective($interpolate, $mdConstant, $mdGridLayout, $mdMedia) {
  return {
    restrict: 'E',
    controller: GridListController,
    scope: {
      mdOnLayout: '&'
    },
    link: postLink
  };

  function postLink(scope, element, attrs, ctrl) {
    element.addClass('_md');     // private md component indicator for styling
    
    // Apply semantics
    element.attr('role', 'list');

    // Provide the controller with a way to trigger layouts.
    ctrl.layoutDelegate = layoutDelegate;

    var invalidateLayout = angular.bind(ctrl, ctrl.invalidateLayout),
        unwatchAttrs = watchMedia();
      scope.$on('$destroy', unwatchMedia);

    /**
     * Watches for changes in media, invalidating layout as necessary.
     */
    function watchMedia() {
      for (var mediaName in $mdConstant.MEDIA) {
        $mdMedia(mediaName); // initialize
        $mdMedia.getQuery($mdConstant.MEDIA[mediaName])
            .addListener(invalidateLayout);
      }
      return $mdMedia.watchResponsiveAttributes(
          ['md-cols', 'md-row-height', 'md-gutter'], attrs, layoutIfMediaMatch);
    }

    function unwatchMedia() {
      ctrl.layoutDelegate = angular.noop;

      unwatchAttrs();
      for (var mediaName in $mdConstant.MEDIA) {
        $mdMedia.getQuery($mdConstant.MEDIA[mediaName])
            .removeListener(invalidateLayout);
      }
    }

    /**
     * Performs grid layout if the provided mediaName matches the currently
     * active media type.
     */
    function layoutIfMediaMatch(mediaName) {
      if (mediaName == null) {
        // TODO(shyndman): It would be nice to only layout if we have
        // instances of attributes using this media type
        ctrl.invalidateLayout();
      } else if ($mdMedia(mediaName)) {
        ctrl.invalidateLayout();
      }
    }

    var lastLayoutProps;

    /**
     * Invokes the layout engine, and uses its results to lay out our
     * tile elements.
     *
     * @param {boolean} tilesInvalidated Whether tiles have been
     *    added/removed/moved since the last layout. This is to avoid situations
     *    where tiles are replaced with properties identical to their removed
     *    counterparts.
     */
    function layoutDelegate(tilesInvalidated) {
      var tiles = getTileElements();
      var props = {
        tileSpans: getTileSpans(tiles),
        colCount: getColumnCount(),
        rowMode: getRowMode(),
        rowHeight: getRowHeight(),
        gutter: getGutter()
      };

      if (!tilesInvalidated && angular.equals(props, lastLayoutProps)) {
        return;
      }

      var performance =
        $mdGridLayout(props.colCount, props.tileSpans, tiles)
          .map(function(tilePositions, rowCount) {
            return {
              grid: {
                element: element,
                style: getGridStyle(props.colCount, rowCount,
                    props.gutter, props.rowMode, props.rowHeight)
              },
              tiles: tilePositions.map(function(ps, i) {
                return {
                  element: angular.element(tiles[i]),
                  style: getTileStyle(ps.position, ps.spans,
                      props.colCount, rowCount,
                      props.gutter, props.rowMode, props.rowHeight)
                }
              })
            }
          })
          .reflow()
          .performance();

      // Report layout
      scope.mdOnLayout({
        $event: {
          performance: performance
        }
      });

      lastLayoutProps = props;
    }

    // Use $interpolate to do some simple string interpolation as a convenience.

    var startSymbol = $interpolate.startSymbol();
    var endSymbol = $interpolate.endSymbol();

    // Returns an expression wrapped in the interpolator's start and end symbols.
    function expr(exprStr) {
      return startSymbol + exprStr + endSymbol;
    }

    // The amount of space a single 1x1 tile would take up (either width or height), used as
    // a basis for other calculations. This consists of taking the base size percent (as would be
    // if evenly dividing the size between cells), and then subtracting the size of one gutter.
    // However, since there are no gutters on the edges, each tile only uses a fration
    // (gutterShare = numGutters / numCells) of the gutter size. (Imagine having one gutter per
    // tile, and then breaking up the extra gutter on the edge evenly among the cells).
    var UNIT = $interpolate(expr('share') + '% - (' + expr('gutter') + ' * ' + expr('gutterShare') + ')');

    // The horizontal or vertical position of a tile, e.g., the 'top' or 'left' property value.
    // The position comes the size of a 1x1 tile plus gutter for each previous tile in the
    // row/column (offset).
    var POSITION  = $interpolate('calc((' + expr('unit') + ' + ' + expr('gutter') + ') * ' + expr('offset') + ')');

    // The actual size of a tile, e.g., width or height, taking rowSpan or colSpan into account.
    // This is computed by multiplying the base unit by the rowSpan/colSpan, and then adding back
    // in the space that the gutter would normally have used (which was already accounted for in
    // the base unit calculation).
    var DIMENSION = $interpolate('calc((' + expr('unit') + ') * ' + expr('span') + ' + (' + expr('span') + ' - 1) * ' + expr('gutter') + ')');

    /**
     * Gets the styles applied to a tile element described by the given parameters.
     * @param {{row: number, col: number}} position The row and column indices of the tile.
     * @param {{row: number, col: number}} spans The rowSpan and colSpan of the tile.
     * @param {number} colCount The number of columns.
     * @param {number} rowCount The number of rows.
     * @param {string} gutter The amount of space between tiles. This will be something like
     *     '5px' or '2em'.
     * @param {string} rowMode The row height mode. Can be one of:
     *     'fixed': all rows have a fixed size, given by rowHeight,
     *     'ratio': row height defined as a ratio to width, or
     *     'fit': fit to the grid-list element height, divinding evenly among rows.
     * @param {string|number} rowHeight The height of a row. This is only used for 'fixed' mode and
     *     for 'ratio' mode. For 'ratio' mode, this is the *ratio* of width-to-height (e.g., 0.75).
     * @returns {Object} Map of CSS properties to be applied to the style element. Will define
     *     values for top, left, width, height, marginTop, and paddingTop.
     */
    function getTileStyle(position, spans, colCount, rowCount, gutter, rowMode, rowHeight) {
      // TODO(shyndman): There are style caching opportunities here.

      // Percent of the available horizontal space that one column takes up.
      var hShare = (1 / colCount) * 100;

      // Fraction of the gutter size that each column takes up.
      var hGutterShare = (colCount - 1) / colCount;

      // Base horizontal size of a column.
      var hUnit = UNIT({share: hShare, gutterShare: hGutterShare, gutter: gutter});

      // The width and horizontal position of each tile is always calculated the same way, but the
      // height and vertical position depends on the rowMode.
      var ltr = document.dir != 'rtl' && document.body.dir != 'rtl';
      var style = ltr ? {
          left: POSITION({ unit: hUnit, offset: position.col, gutter: gutter }),
          width: DIMENSION({ unit: hUnit, span: spans.col, gutter: gutter }),
          // resets
          paddingTop: '',
          marginTop: '',
          top: '',
          height: ''
        } : {
        right: POSITION({ unit: hUnit, offset: position.col, gutter: gutter }),
        width: DIMENSION({ unit: hUnit, span: spans.col, gutter: gutter }),
        // resets
        paddingTop: '',
        marginTop: '',
        top: '',
        height: ''
      };

      switch (rowMode) {
        case 'fixed':
          // In fixed mode, simply use the given rowHeight.
          style.top = POSITION({ unit: rowHeight, offset: position.row, gutter: gutter });
          style.height = DIMENSION({ unit: rowHeight, span: spans.row, gutter: gutter });
          break;

        case 'ratio':
          // Percent of the available vertical space that one row takes up. Here, rowHeight holds
          // the ratio value. For example, if the width:height ratio is 4:3, rowHeight = 1.333.
          var vShare = hShare / rowHeight;

          // Base veritcal size of a row.
          var vUnit = UNIT({ share: vShare, gutterShare: hGutterShare, gutter: gutter });

          // padidngTop and marginTop are used to maintain the given aspect ratio, as
          // a percentage-based value for these properties is applied to the *width* of the
          // containing block. See http://www.w3.org/TR/CSS2/box.html#margin-properties
          style.paddingTop = DIMENSION({ unit: vUnit, span: spans.row, gutter: gutter});
          style.marginTop = POSITION({ unit: vUnit, offset: position.row, gutter: gutter });
          break;

        case 'fit':
          // Fraction of the gutter size that each column takes up.
          var vGutterShare = (rowCount - 1) / rowCount;

          // Percent of the available vertical space that one row takes up.
          var vShare = (1 / rowCount) * 100;

          // Base vertical size of a row.
          var vUnit = UNIT({share: vShare, gutterShare: vGutterShare, gutter: gutter});

          style.top = POSITION({unit: vUnit, offset: position.row, gutter: gutter});
          style.height = DIMENSION({unit: vUnit, span: spans.row, gutter: gutter});
          break;
      }

      return style;
    }

    function getGridStyle(colCount, rowCount, gutter, rowMode, rowHeight) {
      var style = {};

      switch(rowMode) {
        case 'fixed':
          style.height = DIMENSION({ unit: rowHeight, span: rowCount, gutter: gutter });
          style.paddingBottom = '';
          break;

        case 'ratio':
          // rowHeight is width / height
          var hGutterShare = colCount === 1 ? 0 : (colCount - 1) / colCount,
              hShare = (1 / colCount) * 100,
              vShare = hShare * (1 / rowHeight),
              vUnit = UNIT({ share: vShare, gutterShare: hGutterShare, gutter: gutter });

          style.height = '';
          style.paddingBottom = DIMENSION({ unit: vUnit, span: rowCount, gutter: gutter});
          break;

        case 'fit':
          // noop, as the height is user set
          break;
      }

      return style;
    }

    function getTileElements() {
      return [].filter.call(element.children(), function(ele) {
        return ele.tagName == 'MD-GRID-TILE' && !ele.$$mdDestroyed;
      });
    }

    /**
     * Gets an array of objects containing the rowspan and colspan for each tile.
     * @returns {Array<{row: number, col: number}>}
     */
    function getTileSpans(tileElements) {
      return [].map.call(tileElements, function(ele) {
        var ctrl = angular.element(ele).controller('mdGridTile');
        return {
          row: parseInt(
              $mdMedia.getResponsiveAttribute(ctrl.$attrs, 'md-rowspan'), 10) || 1,
          col: parseInt(
              $mdMedia.getResponsiveAttribute(ctrl.$attrs, 'md-colspan'), 10) || 1
        };
      });
    }

    function getColumnCount() {
      var colCount = parseInt($mdMedia.getResponsiveAttribute(attrs, 'md-cols'), 10);
      if (isNaN(colCount)) {
        throw 'md-grid-list: md-cols attribute was not found, or contained a non-numeric value';
      }
      return colCount;
    }

    function getGutter() {
      return applyDefaultUnit($mdMedia.getResponsiveAttribute(attrs, 'md-gutter') || 1);
    }

    function getRowHeight() {
      var rowHeight = $mdMedia.getResponsiveAttribute(attrs, 'md-row-height');
      if (!rowHeight) {
        throw 'md-grid-list: md-row-height attribute was not found';
      }

      switch (getRowMode()) {
        case 'fixed':
          return applyDefaultUnit(rowHeight);
        case 'ratio':
          var whRatio = rowHeight.split(':');
          return parseFloat(whRatio[0]) / parseFloat(whRatio[1]);
        case 'fit':
          return 0; // N/A
      }
    }

    function getRowMode() {
      var rowHeight = $mdMedia.getResponsiveAttribute(attrs, 'md-row-height');
      if (!rowHeight) {
        throw 'md-grid-list: md-row-height attribute was not found';
      }

      if (rowHeight == 'fit') {
        return 'fit';
      } else if (rowHeight.indexOf(':') !== -1) {
        return 'ratio';
      } else {
        return 'fixed';
      }
    }

    function applyDefaultUnit(val) {
      return /\D$/.test(val) ? val : val + 'px';
    }
  }
}

/* @ngInject */
function GridListController($mdUtil) {
  this.layoutInvalidated = false;
  this.tilesInvalidated = false;
  this.$timeout_ = $mdUtil.nextTick;
  this.layoutDelegate = angular.noop;
}

GridListController.prototype = {
  invalidateTiles: function() {
    this.tilesInvalidated = true;
    this.invalidateLayout();
  },

  invalidateLayout: function() {
    if (this.layoutInvalidated) {
      return;
    }
    this.layoutInvalidated = true;
    this.$timeout_(angular.bind(this, this.layout));
  },

  layout: function() {
    try {
      this.layoutDelegate(this.tilesInvalidated);
    } finally {
      this.layoutInvalidated = false;
      this.tilesInvalidated = false;
    }
  }
};


/* @ngInject */
function GridLayoutFactory($mdUtil) {
  var defaultAnimator = GridTileAnimator;

  /**
   * Set the reflow animator callback
   */
  GridLayout.animateWith = function(customAnimator) {
    defaultAnimator = !angular.isFunction(customAnimator) ? GridTileAnimator : customAnimator;
  };

  return GridLayout;

  /**
   * Publish layout function
   */
  function GridLayout(colCount, tileSpans) {
      var self, layoutInfo, gridStyles, layoutTime, mapTime, reflowTime;

      layoutTime = $mdUtil.time(function() {
        layoutInfo = calculateGridFor(colCount, tileSpans);
      });

      return self = {

        /**
         * An array of objects describing each tile's position in the grid.
         */
        layoutInfo: function() {
          return layoutInfo;
        },

        /**
         * Maps grid positioning to an element and a set of styles using the
         * provided updateFn.
         */
        map: function(updateFn) {
          mapTime = $mdUtil.time(function() {
            var info = self.layoutInfo();
            gridStyles = updateFn(info.positioning, info.rowCount);
          });
          return self;
        },

        /**
         * Default animator simply sets the element.css( <styles> ). An alternate
         * animator can be provided as an argument. The function has the following
         * signature:
         *
         *    function({grid: {element: JQLite, style: Object}, tiles: Array<{element: JQLite, style: Object}>)
         */
        reflow: function(animatorFn) {
          reflowTime = $mdUtil.time(function() {
            var animator = animatorFn || defaultAnimator;
            animator(gridStyles.grid, gridStyles.tiles);
          });
          return self;
        },

        /**
         * Timing for the most recent layout run.
         */
        performance: function() {
          return {
            tileCount: tileSpans.length,
            layoutTime: layoutTime,
            mapTime: mapTime,
            reflowTime: reflowTime,
            totalTime: layoutTime + mapTime + reflowTime
          };
        }
      };
    }

  /**
   * Default Gridlist animator simple sets the css for each element;
   * NOTE: any transitions effects must be manually set in the CSS.
   * e.g.
   *
   *  md-grid-tile {
   *    transition: all 700ms ease-out 50ms;
   *  }
   *
   */
  function GridTileAnimator(grid, tiles) {
    grid.element.css(grid.style);
    tiles.forEach(function(t) {
      t.element.css(t.style);
    })
  }

  /**
   * Calculates the positions of tiles.
   *
   * The algorithm works as follows:
   *    An Array<Number> with length colCount (spaceTracker) keeps track of
   *    available tiling positions, where elements of value 0 represents an
   *    empty position. Space for a tile is reserved by finding a sequence of
   *    0s with length <= than the tile's colspan. When such a space has been
   *    found, the occupied tile positions are incremented by the tile's
   *    rowspan value, as these positions have become unavailable for that
   *    many rows.
   *
   *    If the end of a row has been reached without finding space for the
   *    tile, spaceTracker's elements are each decremented by 1 to a minimum
   *    of 0. Rows are searched in this fashion until space is found.
   */
  function calculateGridFor(colCount, tileSpans) {
    var curCol = 0,
        curRow = 0,
        spaceTracker = newSpaceTracker();

    return {
      positioning: tileSpans.map(function(spans, i) {
        return {
          spans: spans,
          position: reserveSpace(spans, i)
        };
      }),
      rowCount: curRow + Math.max.apply(Math, spaceTracker)
    };

    function reserveSpace(spans, i) {
      if (spans.col > colCount) {
        throw 'md-grid-list: Tile at position ' + i + ' has a colspan ' +
            '(' + spans.col + ') that exceeds the column count ' +
            '(' + colCount + ')';
      }

      var start = 0,
          end = 0;

      // TODO(shyndman): This loop isn't strictly necessary if you can
      // determine the minimum number of rows before a space opens up. To do
      // this, recognize that you've iterated across an entire row looking for
      // space, and if so fast-forward by the minimum rowSpan count. Repeat
      // until the required space opens up.
      while (end - start < spans.col) {
        if (curCol >= colCount) {
          nextRow();
          continue;
        }

        start = spaceTracker.indexOf(0, curCol);
        if (start === -1 || (end = findEnd(start + 1)) === -1) {
          start = end = 0;
          nextRow();
          continue;
        }

        curCol = end + 1;
      }

      adjustRow(start, spans.col, spans.row);
      curCol = start + spans.col;

      return {
        col: start,
        row: curRow
      };
    }

    function nextRow() {
      curCol = 0;
      curRow++;
      adjustRow(0, colCount, -1); // Decrement row spans by one
    }

    function adjustRow(from, cols, by) {
      for (var i = from; i < from + cols; i++) {
        spaceTracker[i] = Math.max(spaceTracker[i] + by, 0);
      }
    }

    function findEnd(start) {
      var i;
      for (i = start; i < spaceTracker.length; i++) {
        if (spaceTracker[i] !== 0) {
          return i;
        }
      }

      if (i === spaceTracker.length) {
        return i;
      }
    }

    function newSpaceTracker() {
      var tracker = [];
      for (var i = 0; i < colCount; i++) {
        tracker.push(0);
      }
      return tracker;
    }
  }
}

/**
 * @ngdoc directive
 * @name mdGridTile
 * @module material.components.gridList
 * @restrict E
 * @description
 * Tiles contain the content of an `md-grid-list`. They span one or more grid
 * cells vertically or horizontally, and use `md-grid-tile-{footer,header}` to
 * display secondary content.
 *
 * ### Responsive Attributes
 *
 * The `md-grid-tile` directive supports "responsive" attributes, which allow
 * different `md-rowspan` and `md-colspan` values depending on the currently
 * matching media query.
 *
 * In order to set a responsive attribute, first define the fallback value with
 * the standard attribute name, then add additional attributes with the
 * following convention: `{base-attribute-name}-{media-query-name}="{value}"`
 * (ie. `md-colspan-sm="4"`)
 *
 * @param {number=} md-colspan The number of columns to span (default 1). Cannot
 *    exceed the number of columns in the grid. Supports interpolation.
 * @param {number=} md-rowspan The number of rows to span (default 1). Supports
 *     interpolation.
 *
 * @usage
 * With header:
 * <hljs lang="html">
 * <md-grid-tile>
 *   <md-grid-tile-header>
 *     <h3>This is a header</h3>
 *   </md-grid-tile-header>
 * </md-grid-tile>
 * </hljs>
 *
 * With footer:
 * <hljs lang="html">
 * <md-grid-tile>
 *   <md-grid-tile-footer>
 *     <h3>This is a footer</h3>
 *   </md-grid-tile-footer>
 * </md-grid-tile>
 * </hljs>
 *
 * Spanning multiple rows/columns:
 * <hljs lang="html">
 * <md-grid-tile md-colspan="2" md-rowspan="3">
 * </md-grid-tile>
 * </hljs>
 *
 * Responsive attributes:
 * <hljs lang="html">
 * <md-grid-tile md-colspan="1" md-colspan-sm="3" md-colspan-md="5">
 * </md-grid-tile>
 * </hljs>
 */
function GridTileDirective($mdMedia) {
  return {
    restrict: 'E',
    require: '^mdGridList',
    template: '<figure ng-transclude></figure>',
    transclude: true,
    scope: {},
    // Simple controller that exposes attributes to the grid directive
    controller: ["$attrs", function($attrs) {
      this.$attrs = $attrs;
    }],
    link: postLink
  };

  function postLink(scope, element, attrs, gridCtrl) {
    // Apply semantics
    element.attr('role', 'listitem');

    // If our colspan or rowspan changes, trigger a layout
    var unwatchAttrs = $mdMedia.watchResponsiveAttributes(['md-colspan', 'md-rowspan'],
        attrs, angular.bind(gridCtrl, gridCtrl.invalidateLayout));

    // Tile registration/deregistration
    gridCtrl.invalidateTiles();
    scope.$on('$destroy', function() {
      // Mark the tile as destroyed so it is no longer considered in layout,
      // even if the DOM element sticks around (like during a leave animation)
      element[0].$$mdDestroyed = true;
      unwatchAttrs();
      gridCtrl.invalidateLayout();
    });

    if (angular.isDefined(scope.$parent.$index)) {
      scope.$watch(function() { return scope.$parent.$index; },
        function indexChanged(newIdx, oldIdx) {
          if (newIdx === oldIdx) {
            return;
          }
          gridCtrl.invalidateTiles();
        });
    }
  }
}


function GridTileCaptionDirective() {
  return {
    template: '<figcaption ng-transclude></figcaption>',
    transclude: true
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.input
 */
mdInputContainerDirective.$inject = ["$mdTheming", "$parse"];
inputTextareaDirective.$inject = ["$mdUtil", "$window", "$mdAria", "$timeout", "$mdGesture"];
mdMaxlengthDirective.$inject = ["$animate", "$mdUtil"];
placeholderDirective.$inject = ["$compile"];
ngMessageDirective.$inject = ["$mdUtil"];
mdSelectOnFocusDirective.$inject = ["$timeout"];
mdInputInvalidMessagesAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil", "$log"];
ngMessagesAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil", "$log"];
ngMessageAnimation.$inject = ["$$AnimateRunner", "$animateCss", "$mdUtil", "$log"];
var inputModule = angular.module('material.components.input', [
    'material.core'
  ])
  .directive('mdInputContainer', mdInputContainerDirective)
  .directive('label', labelDirective)
  .directive('input', inputTextareaDirective)
  .directive('textarea', inputTextareaDirective)
  .directive('mdMaxlength', mdMaxlengthDirective)
  .directive('placeholder', placeholderDirective)
  .directive('ngMessages', ngMessagesDirective)
  .directive('ngMessage', ngMessageDirective)
  .directive('ngMessageExp', ngMessageDirective)
  .directive('mdSelectOnFocus', mdSelectOnFocusDirective)

  .animation('.md-input-invalid', mdInputInvalidMessagesAnimation)
  .animation('.md-input-messages-animation', ngMessagesAnimation)
  .animation('.md-input-message-animation', ngMessageAnimation);

// If we are running inside of tests; expose some extra services so that we can test them
if (window._mdMocksIncluded) {
  inputModule.service('$$mdInput', function() {
    return {
      // special accessor to internals... useful for testing
      messages: {
        show        : showInputMessages,
        hide        : hideInputMessages,
        getElement  : getMessagesElement
      }
    }
  })

  // Register a service for each animation so that we can easily inject them into unit tests
  .service('mdInputInvalidAnimation', mdInputInvalidMessagesAnimation)
  .service('mdInputMessagesAnimation', ngMessagesAnimation)
  .service('mdInputMessageAnimation', ngMessageAnimation);
}

/**
 * @ngdoc directive
 * @name mdInputContainer
 * @module material.components.input
 *
 * @restrict E
 *
 * @description
 * `<md-input-container>` is the parent of any input or textarea element.
 *
 * Input and textarea elements will not behave properly unless the md-input-container
 * parent is provided.
 *
 * A single `<md-input-container>` should contain only one `<input>` element, otherwise it will throw an error.
 *
 * <b>Exception:</b> Hidden inputs (`<input type="hidden" />`) are ignored and will not throw an error, so
 * you may combine these with other inputs.
 *
 * <b>Note:</b> When using `ngMessages` with your input element, make sure the message and container elements
 * are *block* elements, otherwise animations applied to the messages will not look as intended. Either use a `div` and
 * apply the `ng-message` and `ng-messages` classes respectively, or use the `md-block` class on your element.
 *
 * @param md-is-error {expression=} When the given expression evaluates to true, the input container
 *   will go into error state. Defaults to erroring if the input has been touched and is invalid.
 * @param md-no-float {boolean=} When present, `placeholder` attributes on the input will not be converted to floating
 *   labels.
 *
 * @usage
 * <hljs lang="html">
 *
 * <md-input-container>
 *   <label>Username</label>
 *   <input type="text" ng-model="user.name">
 * </md-input-container>
 *
 * <md-input-container>
 *   <label>Description</label>
 *   <textarea ng-model="user.description"></textarea>
 * </md-input-container>
 *
 * </hljs>
 *
 * <h3>When disabling floating labels</h3>
 * <hljs lang="html">
 *
 * <md-input-container md-no-float>
 *   <input type="text" placeholder="Non-Floating Label">
 * </md-input-container>
 *
 * </hljs>
 */
function mdInputContainerDirective($mdTheming, $parse) {

  ContainerCtrl.$inject = ["$scope", "$element", "$attrs", "$animate"];
  var INPUT_TAGS = ['INPUT', 'TEXTAREA', 'SELECT', 'MD-SELECT'];

  var LEFT_SELECTORS = INPUT_TAGS.reduce(function(selectors, isel) {
    return selectors.concat(['md-icon ~ ' + isel, '.md-icon ~ ' + isel]);
  }, []).join(",");

  var RIGHT_SELECTORS = INPUT_TAGS.reduce(function(selectors, isel) {
    return selectors.concat([isel + ' ~ md-icon', isel + ' ~ .md-icon']);
  }, []).join(",");

  return {
    restrict: 'E',
    compile: compile,
    controller: ContainerCtrl
  };

  function compile(tElement) {
    // Check for both a left & right icon
    var leftIcon = tElement[0].querySelector(LEFT_SELECTORS);
    var rightIcon = tElement[0].querySelector(RIGHT_SELECTORS);

    if (leftIcon) { tElement.addClass('md-icon-left'); }
    if (rightIcon) { tElement.addClass('md-icon-right'); }

    return function postLink(scope, element) {
      $mdTheming(element);
    };
  }

  function ContainerCtrl($scope, $element, $attrs, $animate) {
    var self = this;

    self.isErrorGetter = $attrs.mdIsError && $parse($attrs.mdIsError);

    self.delegateClick = function() {
      self.input.focus();
    };
    self.element = $element;
    self.setFocused = function(isFocused) {
      $element.toggleClass('md-input-focused', !!isFocused);
    };
    self.setHasValue = function(hasValue) {
      $element.toggleClass('md-input-has-value', !!hasValue);
    };
    self.setHasPlaceholder = function(hasPlaceholder) {
      $element.toggleClass('md-input-has-placeholder', !!hasPlaceholder);
    };
    self.setInvalid = function(isInvalid) {
      if (isInvalid) {
        $animate.addClass($element, 'md-input-invalid');
      } else {
        $animate.removeClass($element, 'md-input-invalid');
      }
    };
    $scope.$watch(function() {
      return self.label && self.input;
    }, function(hasLabelAndInput) {
      if (hasLabelAndInput && !self.label.attr('for')) {
        self.label.attr('for', self.input.attr('id'));
      }
    });
  }
}

function labelDirective() {
  return {
    restrict: 'E',
    require: '^?mdInputContainer',
    link: function(scope, element, attr, containerCtrl) {
      if (!containerCtrl || attr.mdNoFloat || element.hasClass('md-container-ignore')) return;

      containerCtrl.label = element;
      scope.$on('$destroy', function() {
        containerCtrl.label = null;
      });
    }
  };
}

/**
 * @ngdoc directive
 * @name mdInput
 * @restrict E
 * @module material.components.input
 *
 * @description
 * You can use any `<input>` or `<textarea>` element as a child of an `<md-input-container>`. This
 * allows you to build complex forms for data entry.
 *
 * When the input is required and uses a floating label, then the label will automatically contain
 * an asterisk (`*`).<br/>
 * This behavior can be disabled by using the `md-no-asterisk` attribute.
 *
 * @param {number=} md-maxlength The maximum number of characters allowed in this input. If this is
 *   specified, a character counter will be shown underneath the input.<br/><br/>
 *   The purpose of **`md-maxlength`** is exactly to show the max length counter text. If you don't
 *   want the counter text and only need "plain" validation, you can use the "simple" `ng-maxlength`
 *   or maxlength attributes.<br/><br/>
 *   **Note:** Only valid for text/string inputs (not numeric).
 *
 * @param {string=} aria-label Aria-label is required when no label is present.  A warning message
 *   will be logged in the console if not present.
 * @param {string=} placeholder An alternative approach to using aria-label when the label is not
 *   PRESENT. The placeholder text is copied to the aria-label attribute.
 * @param md-no-autogrow {boolean=} When present, textareas will not grow automatically.
 * @param md-no-asterisk {boolean=} When present, an asterisk will not be appended to the inputs floating label
 * @param md-no-resize {boolean=} Disables the textarea resize handle.
 * @param {number=} max-rows The maximum amount of rows for a textarea.
 * @param md-detect-hidden {boolean=} When present, textareas will be sized properly when they are
 *   revealed after being hidden. This is off by default for performance reasons because it
 *   guarantees a reflow every digest cycle.
 *
 * @usage
 * <hljs lang="html">
 * <md-input-container>
 *   <label>Color</label>
 *   <input type="text" ng-model="color" required md-maxlength="10">
 * </md-input-container>
 * </hljs>
 *
 * <h3>With Errors</h3>
 *
 * `md-input-container` also supports errors using the standard `ng-messages` directives and
 * animates the messages when they become visible using from the `ngEnter`/`ngLeave` events or
 * the `ngShow`/`ngHide` events.
 *
 * By default, the messages will be hidden until the input is in an error state. This is based off
 * of the `md-is-error` expression of the `md-input-container`. This gives the user a chance to
 * fill out the form before the errors become visible.
 *
 * <hljs lang="html">
 * <form name="colorForm">
 *   <md-input-container>
 *     <label>Favorite Color</label>
 *     <input name="favoriteColor" ng-model="favoriteColor" required>
 *     <div ng-messages="colorForm.favoriteColor.$error">
 *       <div ng-message="required">This is required!</div>
 *     </div>
 *   </md-input-container>
 * </form>
 * </hljs>
 *
 * We automatically disable this auto-hiding functionality if you provide any of the following
 * visibility directives on the `ng-messages` container:
 *
 *  - `ng-if`
 *  - `ng-show`/`ng-hide`
 *  - `ng-switch-when`/`ng-switch-default`
 *
 * You can also disable this functionality manually by adding the `md-auto-hide="false"` expression
 * to the `ng-messages` container. This may be helpful if you always want to see the error messages
 * or if you are building your own visibilty directive.
 *
 * _<b>Note:</b> The `md-auto-hide` attribute is a static string that is  only checked upon
 * initialization of the `ng-messages` directive to see if it equals the string `false`._
 *
 * <hljs lang="html">
 * <form name="userForm">
 *   <md-input-container>
 *     <label>Last Name</label>
 *     <input name="lastName" ng-model="lastName" required md-maxlength="10" minlength="4">
 *     <div ng-messages="userForm.lastName.$error" ng-show="userForm.lastName.$dirty">
 *       <div ng-message="required">This is required!</div>
 *       <div ng-message="md-maxlength">That's too long!</div>
 *       <div ng-message="minlength">That's too short!</div>
 *     </div>
 *   </md-input-container>
 *   <md-input-container>
 *     <label>Biography</label>
 *     <textarea name="bio" ng-model="biography" required md-maxlength="150"></textarea>
 *     <div ng-messages="userForm.bio.$error" ng-show="userForm.bio.$dirty">
 *       <div ng-message="required">This is required!</div>
 *       <div ng-message="md-maxlength">That's too long!</div>
 *     </div>
 *   </md-input-container>
 *   <md-input-container>
 *     <input aria-label='title' ng-model='title'>
 *   </md-input-container>
 *   <md-input-container>
 *     <input placeholder='title' ng-model='title'>
 *   </md-input-container>
 * </form>
 * </hljs>
 *
 * <h3>Notes</h3>
 *
 * - Requires [ngMessages](https://docs.angularjs.org/api/ngMessages).
 * - Behaves like the [AngularJS input directive](https://docs.angularjs.org/api/ng/directive/input).
 *
 * The `md-input` and `md-input-container` directives use very specific positioning to achieve the
 * error animation effects. Therefore, it is *not* advised to use the Layout system inside of the
 * `<md-input-container>` tags. Instead, use relative or absolute positioning.
 *
 *
 * <h3>Textarea directive</h3>
 * The `textarea` element within a `md-input-container` has the following specific behavior:
 * - By default the `textarea` grows as the user types. This can be disabled via the `md-no-autogrow`
 * attribute.
 * - If a `textarea` has the `rows` attribute, it will treat the `rows` as the minimum height and will
 * continue growing as the user types. For example a textarea with `rows="3"` will be 3 lines of text
 * high initially. If no rows are specified, the directive defaults to 1.
 * - The textarea's height gets set on initialization, as well as while the user is typing. In certain situations
 * (e.g. while animating) the directive might have been initialized, before the element got it's final height. In
 * those cases, you can trigger a resize manually by broadcasting a `md-resize-textarea` event on the scope.
 * - If you want a `textarea` to stop growing at a certain point, you can specify the `max-rows` attribute.
 * - The textarea's bottom border acts as a handle which users can drag, in order to resize the element vertically.
 * Once the user has resized a `textarea`, the autogrowing functionality becomes disabled. If you don't want a
 * `textarea` to be resizeable by the user, you can add the `md-no-resize` attribute.
 */

function inputTextareaDirective($mdUtil, $window, $mdAria, $timeout, $mdGesture) {
  return {
    restrict: 'E',
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    link: postLink
  };

  function postLink(scope, element, attr, ctrls) {

    var containerCtrl = ctrls[0];
    var hasNgModel = !!ctrls[1];
    var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();
    var parentForm = ctrls[2];
    var isReadonly = angular.isDefined(attr.readonly);
    var mdNoAsterisk = $mdUtil.parseAttributeBoolean(attr.mdNoAsterisk);
    var tagName = element[0].tagName.toLowerCase();


    if (!containerCtrl) return;
    if (attr.type === 'hidden') {
      element.attr('aria-hidden', 'true');
      return;
    } else if (containerCtrl.input) {
      if (containerCtrl.input[0].contains(element[0])) {
        return;
      } else {
        throw new Error("<md-input-container> can only have *one* <input>, <textarea> or <md-select> child element!");
      }
    }
    containerCtrl.input = element;

    setupAttributeWatchers();

    // Add an error spacer div after our input to provide space for the char counter and any ng-messages
    var errorsSpacer = angular.element('<div class="md-errors-spacer">');
    element.after(errorsSpacer);

    var placeholderText = angular.isString(attr.placeholder) ? attr.placeholder.trim() : '';
    if (!containerCtrl.label && !placeholderText.length) {
      $mdAria.expect(element, 'aria-label');
    }

    element.addClass('md-input');
    if (!element.attr('id')) {
      element.attr('id', 'input_' + $mdUtil.nextUid());
    }

    // This works around a Webkit issue where number inputs, placed in a flexbox, that have
    // a `min` and `max` will collapse to about 1/3 of their proper width. Please check #7349
    // for more info. Also note that we don't override the `step` if the user has specified it,
    // in order to prevent some unexpected behaviour.
    if (tagName === 'input' && attr.type === 'number' && attr.min && attr.max && !attr.step) {
      element.attr('step', 'any');
    } else if (tagName === 'textarea') {
      setupTextarea();
    }

    // If the input doesn't have an ngModel, it may have a static value. For that case,
    // we have to do one initial check to determine if the container should be in the
    // "has a value" state.
    if (!hasNgModel) {
      inputCheckValue();
    }

    var isErrorGetter = containerCtrl.isErrorGetter || function() {
      return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (parentForm && parentForm.$submitted));
    };

    scope.$watch(isErrorGetter, containerCtrl.setInvalid);

    // When the developer uses the ngValue directive for the input, we have to observe the attribute, because
    // AngularJS's ngValue directive is just setting the `value` attribute.
    if (attr.ngValue) {
      attr.$observe('value', inputCheckValue);
    }

    ngModelCtrl.$parsers.push(ngModelPipelineCheckValue);
    ngModelCtrl.$formatters.push(ngModelPipelineCheckValue);

    element.on('input', inputCheckValue);

    if (!isReadonly) {
      element
        .on('focus', function(ev) {
          $mdUtil.nextTick(function() {
            containerCtrl.setFocused(true);
          });
        })
        .on('blur', function(ev) {
          $mdUtil.nextTick(function() {
            containerCtrl.setFocused(false);
            inputCheckValue();
          });
        });
    }

    scope.$on('$destroy', function() {
      containerCtrl.setFocused(false);
      containerCtrl.setHasValue(false);
      containerCtrl.input = null;
    });

    /** Gets run through ngModel's pipeline and set the `has-value` class on the container. */
    function ngModelPipelineCheckValue(arg) {
      containerCtrl.setHasValue(!ngModelCtrl.$isEmpty(arg));
      return arg;
    }

    function setupAttributeWatchers() {
      if (containerCtrl.label) {
        attr.$observe('required', function (value) {
          // We don't need to parse the required value, it's always a boolean because of angular's
          // required directive.
          containerCtrl.label.toggleClass('md-required', value && !mdNoAsterisk);
        });
      }
    }

    function inputCheckValue() {
      // An input's value counts if its length > 0,
      // or if the input's validity state says it has bad input (eg string in a number input)
      containerCtrl.setHasValue(element.val().length > 0 || (element[0].validity || {}).badInput);
    }

    function setupTextarea() {
      var isAutogrowing = !attr.hasOwnProperty('mdNoAutogrow');

      attachResizeHandle();

      if (!isAutogrowing) return;

      // Can't check if height was or not explicity set,
      // so rows attribute will take precedence if present
      var minRows = attr.hasOwnProperty('rows') ? parseInt(attr.rows) : NaN;
      var maxRows = attr.hasOwnProperty('maxRows') ? parseInt(attr.maxRows) : NaN;
      var scopeResizeListener = scope.$on('md-resize-textarea', growTextarea);
      var lineHeight = null;
      var node = element[0];

      // This timeout is necessary, because the browser needs a little bit
      // of time to calculate the `clientHeight` and `scrollHeight`.
      $timeout(function() {
        $mdUtil.nextTick(growTextarea);
      }, 10, false);

      // We could leverage ngModel's $parsers here, however it
      // isn't reliable, because AngularJS trims the input by default,
      // which means that growTextarea won't fire when newlines and
      // spaces are added.
      element.on('input', growTextarea);

      // We should still use the $formatters, because they fire when
      // the value was changed from outside the textarea.
      if (hasNgModel) {
        ngModelCtrl.$formatters.push(formattersListener);
      }

      if (!minRows) {
        element.attr('rows', 1);
      }

      angular.element($window).on('resize', growTextarea);
      scope.$on('$destroy', disableAutogrow);

      function growTextarea() {
        // temporarily disables element's flex so its height 'runs free'
        element
          .attr('rows', 1)
          .css('height', 'auto')
          .addClass('md-no-flex');

        var height = getHeight();

        if (!lineHeight) {
          // offsetHeight includes padding which can throw off our value
          var originalPadding = element[0].style.padding || '';
          lineHeight = element.css('padding', 0).prop('offsetHeight');
          element[0].style.padding = originalPadding;
        }

        if (minRows && lineHeight) {
          height = Math.max(height, lineHeight * minRows);
        }

        if (maxRows && lineHeight) {
          var maxHeight = lineHeight * maxRows;

          if (maxHeight < height) {
            element.attr('md-no-autogrow', '');
            height = maxHeight;
          } else {
            element.removeAttr('md-no-autogrow');
          }
        }

        if (lineHeight) {
          element.attr('rows', Math.round(height / lineHeight));
        }

        element
          .css('height', height + 'px')
          .removeClass('md-no-flex');
      }

      function getHeight() {
        var offsetHeight = node.offsetHeight;
        var line = node.scrollHeight - offsetHeight;
        return offsetHeight + Math.max(line, 0);
      }

      function formattersListener(value) {
        $mdUtil.nextTick(growTextarea);
        return value;
      }

      function disableAutogrow() {
        if (!isAutogrowing) return;

        isAutogrowing = false;
        angular.element($window).off('resize', growTextarea);
        scopeResizeListener && scopeResizeListener();
        element
          .attr('md-no-autogrow', '')
          .off('input', growTextarea);

        if (hasNgModel) {
          var listenerIndex = ngModelCtrl.$formatters.indexOf(formattersListener);

          if (listenerIndex > -1) {
            ngModelCtrl.$formatters.splice(listenerIndex, 1);
          }
        }
      }

      function attachResizeHandle() {
        if (attr.hasOwnProperty('mdNoResize')) return;

        var handle = angular.element('<div class="md-resize-handle"></div>');
        var isDragging = false;
        var dragStart = null;
        var startHeight = 0;
        var container = containerCtrl.element;
        var dragGestureHandler = $mdGesture.register(handle, 'drag', { horizontal: false });


        element.wrap('<div class="md-resize-wrapper">').after(handle);
        handle.on('mousedown', onMouseDown);

        container
          .on('$md.dragstart', onDragStart)
          .on('$md.drag', onDrag)
          .on('$md.dragend', onDragEnd);

        scope.$on('$destroy', function() {
          handle
            .off('mousedown', onMouseDown)
            .remove();

          container
            .off('$md.dragstart', onDragStart)
            .off('$md.drag', onDrag)
            .off('$md.dragend', onDragEnd);

          dragGestureHandler();
          handle = null;
          container = null;
          dragGestureHandler = null;
        });

        function onMouseDown(ev) {
          ev.preventDefault();
          isDragging = true;
          dragStart = ev.clientY;
          startHeight = parseFloat(element.css('height')) || element.prop('offsetHeight');
        }

        function onDragStart(ev) {
          if (!isDragging) return;
          ev.preventDefault();
          disableAutogrow();
          container.addClass('md-input-resized');
        }

        function onDrag(ev) {
          if (!isDragging) return;

          element.css('height', (startHeight + ev.pointer.distanceY) + 'px');
        }

        function onDragEnd(ev) {
          if (!isDragging) return;
          isDragging = false;
          container.removeClass('md-input-resized');
        }
      }

      // Attach a watcher to detect when the textarea gets shown.
      if (attr.hasOwnProperty('mdDetectHidden')) {

        var handleHiddenChange = function() {
          var wasHidden = false;

          return function() {
            var isHidden = node.offsetHeight === 0;

            if (isHidden === false && wasHidden === true) {
              growTextarea();
            }

            wasHidden = isHidden;
          };
        }();

        // Check every digest cycle whether the visibility of the textarea has changed.
        // Queue up to run after the digest cycle is complete.
        scope.$watch(function() {
          $mdUtil.nextTick(handleHiddenChange, false);
          return true;
        });
      }
    }
  }
}

function mdMaxlengthDirective($animate, $mdUtil) {
  return {
    restrict: 'A',
    require: ['ngModel', '^mdInputContainer'],
    link: postLink
  };

  function postLink(scope, element, attr, ctrls) {
    var maxlength;
    var ngModelCtrl = ctrls[0];
    var containerCtrl = ctrls[1];
    var charCountEl, errorsSpacer;

    // Wait until the next tick to ensure that the input has setup the errors spacer where we will
    // append our counter
    $mdUtil.nextTick(function() {
      errorsSpacer = angular.element(containerCtrl.element[0].querySelector('.md-errors-spacer'));
      charCountEl = angular.element('<div class="md-char-counter">');

      // Append our character counter inside the errors spacer
      errorsSpacer.append(charCountEl);

      // Stop model from trimming. This makes it so whitespace
      // over the maxlength still counts as invalid.
      attr.$set('ngTrim', 'false');

      scope.$watch(attr.mdMaxlength, function(value) {
        maxlength = value;
        if (angular.isNumber(value) && value > 0) {
          if (!charCountEl.parent().length) {
            $animate.enter(charCountEl, errorsSpacer);
          }
          renderCharCount();
        } else {
          $animate.leave(charCountEl);
        }
      });

      ngModelCtrl.$validators['md-maxlength'] = function(modelValue, viewValue) {
        if (!angular.isNumber(maxlength) || maxlength < 0) {
          return true;
        }

        // We always update the char count, when the modelValue has changed.
        // Using the $validators for triggering the update works very well.
        renderCharCount();

        return ( modelValue || element.val() || viewValue || '' ).length <= maxlength;
      };
    });

    function renderCharCount(value) {
      // If we have not been appended to the body yet; do not render
      if (!charCountEl.parent) {
        return value;
      }

      // Force the value into a string since it may be a number,
      // which does not have a length property.
      charCountEl.text(String(element.val() || value || '').length + ' / ' + maxlength);
      return value;
    }
  }
}

function placeholderDirective($compile) {
  return {
    restrict: 'A',
    require: '^^?mdInputContainer',
    priority: 200,
    link: {
      // Note that we need to do this in the pre-link, as opposed to the post link, if we want to
      // support data bindings in the placeholder. This is necessary, because we have a case where
      // we transfer the placeholder value to the `<label>` and we remove it from the original `<input>`.
      // If we did this in the post-link, AngularJS would have set up the observers already and would be
      // re-adding the attribute, even though we removed it from the element.
      pre: preLink
    }
  };

  function preLink(scope, element, attr, inputContainer) {
    // If there is no input container, just return
    if (!inputContainer) return;

    var label = inputContainer.element.find('label');
    var noFloat = inputContainer.element.attr('md-no-float');

    // If we have a label, or they specify the md-no-float attribute, just return
    if ((label && label.length) || noFloat === '' || scope.$eval(noFloat)) {
      // Add a placeholder class so we can target it in the CSS
      inputContainer.setHasPlaceholder(true);
      return;
    }

    // md-select handles placeholders on it's own
    if (element[0].nodeName != 'MD-SELECT') {
      // Move the placeholder expression to the label
      var newLabel = angular.element('<label ng-click="delegateClick()" tabindex="-1">' + attr.placeholder + '</label>');

      // Note that we unset it via `attr`, in order to get AngularJS
      // to remove any observers that it might have set up. Otherwise
      // the attribute will be added on the next digest.
      attr.$set('placeholder', null);

      // We need to compile the label manually in case it has any bindings.
      // A gotcha here is that we first add the element to the DOM and we compile
      // it later. This is necessary, because if we compile the element beforehand,
      // it won't be able to find the `mdInputContainer` controller.
      inputContainer.element
        .addClass('md-icon-float')
        .prepend(newLabel);

      $compile(newLabel)(scope);
    }
  }
}

/**
 * @ngdoc directive
 * @name mdSelectOnFocus
 * @module material.components.input
 *
 * @restrict A
 *
 * @description
 * The `md-select-on-focus` directive allows you to automatically select the element's input text on focus.
 *
 * <h3>Notes</h3>
 * - The use of `md-select-on-focus` is restricted to `<input>` and `<textarea>` elements.
 *
 * @usage
 * <h3>Using with an Input</h3>
 * <hljs lang="html">
 *
 * <md-input-container>
 *   <label>Auto Select</label>
 *   <input type="text" md-select-on-focus>
 * </md-input-container>
 * </hljs>
 *
 * <h3>Using with a Textarea</h3>
 * <hljs lang="html">
 *
 * <md-input-container>
 *   <label>Auto Select</label>
 *   <textarea md-select-on-focus>This text will be selected on focus.</textarea>
 * </md-input-container>
 *
 * </hljs>
 */
function mdSelectOnFocusDirective($timeout) {

  return {
    restrict: 'A',
    link: postLink
  };

  function postLink(scope, element, attr) {
    if (element[0].nodeName !== 'INPUT' && element[0].nodeName !== "TEXTAREA") return;

    var preventMouseUp = false;

    element
      .on('focus', onFocus)
      .on('mouseup', onMouseUp);

    scope.$on('$destroy', function() {
      element
        .off('focus', onFocus)
        .off('mouseup', onMouseUp);
    });

    function onFocus() {
      preventMouseUp = true;

      $timeout(function() {
        // Use HTMLInputElement#select to fix firefox select issues.
        // The debounce is here for Edge's sake, otherwise the selection doesn't work.
        element[0].select();

        // This should be reset from inside the `focus`, because the event might
        // have originated from something different than a click, e.g. a keyboard event.
        preventMouseUp = false;
      }, 1, false);
    }

    // Prevents the default action of the first `mouseup` after a focus.
    // This is necessary, because browsers fire a `mouseup` right after the element
    // has been focused. In some browsers (Firefox in particular) this can clear the
    // selection. There are examples of the problem in issue #7487.
    function onMouseUp(event) {
      if (preventMouseUp) {
        event.preventDefault();
      }
    }
  }
}

var visibilityDirectives = ['ngIf', 'ngShow', 'ngHide', 'ngSwitchWhen', 'ngSwitchDefault'];
function ngMessagesDirective() {
  return {
    restrict: 'EA',
    link: postLink,

    // This is optional because we don't want target *all* ngMessage instances, just those inside of
    // mdInputContainer.
    require: '^^?mdInputContainer'
  };

  function postLink(scope, element, attrs, inputContainer) {
    // If we are not a child of an input container, don't do anything
    if (!inputContainer) return;

    // Add our animation class
    element.toggleClass('md-input-messages-animation', true);

    // Add our md-auto-hide class to automatically hide/show messages when container is invalid
    element.toggleClass('md-auto-hide', true);

    // If we see some known visibility directives, remove the md-auto-hide class
    if (attrs.mdAutoHide == 'false' || hasVisibiltyDirective(attrs)) {
      element.toggleClass('md-auto-hide', false);
    }
  }

  function hasVisibiltyDirective(attrs) {
    return visibilityDirectives.some(function(attr) {
      return attrs[attr];
    });
  }
}

function ngMessageDirective($mdUtil) {
  return {
    restrict: 'EA',
    compile: compile,
    priority: 100
  };

  function compile(tElement) {
    if (!isInsideInputContainer(tElement)) {

      // When the current element is inside of a document fragment, then we need to check for an input-container
      // in the postLink, because the element will be later added to the DOM and is currently just in a temporary
      // fragment, which causes the input-container check to fail.
      if (isInsideFragment()) {
        return function (scope, element) {
          if (isInsideInputContainer(element)) {
            // Inside of the postLink function, a ngMessage directive will be a comment element, because it's
            // currently hidden. To access the shown element, we need to use the element from the compile function.
            initMessageElement(tElement);
          }
        };
      }
    } else {
      initMessageElement(tElement);
    }

    function isInsideFragment() {
      var nextNode = tElement[0];
      while (nextNode = nextNode.parentNode) {
        if (nextNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
          return true;
        }
      }
      return false;
    }

    function isInsideInputContainer(element) {
      return !!$mdUtil.getClosest(element, "md-input-container");
    }

    function initMessageElement(element) {
      // Add our animation class
      element.toggleClass('md-input-message-animation', true);
    }
  }
}

var $$AnimateRunner, $animateCss, $mdUtil, $log;

function mdInputInvalidMessagesAnimation($$AnimateRunner, $animateCss, $mdUtil, $log) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil, $log);

  return {
    addClass: function(element, className, done) {
      showInputMessages(element, done);
    }

    // NOTE: We do not need the removeClass method, because the message ng-leave animation will fire
  };
}

function ngMessagesAnimation($$AnimateRunner, $animateCss, $mdUtil, $log) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil, $log);

  return {
    enter: function(element, done) {
      showInputMessages(element, done);
    },

    leave: function(element, done) {
      hideInputMessages(element, done);
    },

    addClass: function(element, className, done) {
      if (className == "ng-hide") {
        hideInputMessages(element, done);
      } else {
        done();
      }
    },

    removeClass: function(element, className, done) {
      if (className == "ng-hide") {
        showInputMessages(element, done);
      } else {
        done();
      }
    }
  };
}

function ngMessageAnimation($$AnimateRunner, $animateCss, $mdUtil, $log) {
  saveSharedServices($$AnimateRunner, $animateCss, $mdUtil, $log);

  return {
    enter: function(element, done) {
      var animator = showMessage(element);

      animator.start().done(done);
    },

    leave: function(element, done) {
      var animator = hideMessage(element);

      animator.start().done(done);
    }
  };
}

function showInputMessages(element, done) {
  var animators = [], animator;
  var messages = getMessagesElement(element);
  var children = messages.children();

  if (messages.length == 0 || children.length == 0) {
    $log.warn('mdInput messages show animation called on invalid messages element: ', element);
    done();
    return;
  }

  angular.forEach(children, function(child) {
    animator = showMessage(angular.element(child));

    animators.push(animator.start());
  });

  $$AnimateRunner.all(animators, done);
}

function hideInputMessages(element, done) {
  var animators = [], animator;
  var messages = getMessagesElement(element);
  var children = messages.children();

  if (messages.length == 0 || children.length == 0) {
    $log.warn('mdInput messages hide animation called on invalid messages element: ', element);
    done();
    return;
  }

  angular.forEach(children, function(child) {
    animator = hideMessage(angular.element(child));

    animators.push(animator.start());
  });

  $$AnimateRunner.all(animators, done);
}

function showMessage(element) {
  var height = parseInt(window.getComputedStyle(element[0]).height);
  var topMargin = parseInt(window.getComputedStyle(element[0]).marginTop);

  var messages = getMessagesElement(element);
  var container = getInputElement(element);

  // Check to see if the message is already visible so we can skip
  var alreadyVisible = (topMargin > -height);

  // If we have the md-auto-hide class, the md-input-invalid animation will fire, so we can skip
  if (alreadyVisible || (messages.hasClass('md-auto-hide') && !container.hasClass('md-input-invalid'))) {
    return $animateCss(element, {});
  }

  return $animateCss(element, {
    event: 'enter',
    structural: true,
    from: {"opacity": 0, "margin-top": -height + "px"},
    to: {"opacity": 1, "margin-top": "0"},
    duration: 0.3
  });
}

function hideMessage(element) {
  var height = element[0].offsetHeight;
  var styles = window.getComputedStyle(element[0]);

  // If we are already hidden, just return an empty animation
  if (parseInt(styles.opacity) === 0) {
    return $animateCss(element, {});
  }

  // Otherwise, animate
  return $animateCss(element, {
    event: 'leave',
    structural: true,
    from: {"opacity": 1, "margin-top": 0},
    to: {"opacity": 0, "margin-top": -height + "px"},
    duration: 0.3
  });
}

function getInputElement(element) {
  var inputContainer = element.controller('mdInputContainer');

  return inputContainer.element;
}

function getMessagesElement(element) {
  // If we ARE the messages element, just return ourself
  if (element.hasClass('md-input-messages-animation')) {
    return element;
  }

  // If we are a ng-message element, we need to traverse up the DOM tree
  if (element.hasClass('md-input-message-animation')) {
    return angular.element($mdUtil.getClosest(element, function(node) {
      return node.classList.contains('md-input-messages-animation');
    }));
  }

  // Otherwise, we can traverse down
  return angular.element(element[0].querySelector('.md-input-messages-animation'));
}

function saveSharedServices(_$$AnimateRunner_, _$animateCss_, _$mdUtil_, _$log_) {
  $$AnimateRunner = _$$AnimateRunner_;
  $animateCss = _$animateCss_;
  $mdUtil = _$mdUtil_;
  $log = _$log_;
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.list
 * @description
 * List module
 */
MdListController.$inject = ["$scope", "$element", "$mdListInkRipple"];
mdListDirective.$inject = ["$mdTheming"];
mdListItemDirective.$inject = ["$mdAria", "$mdConstant", "$mdUtil", "$timeout"];
angular.module('material.components.list', [
  'material.core'
])
  .controller('MdListController', MdListController)
  .directive('mdList', mdListDirective)
  .directive('mdListItem', mdListItemDirective);

/**
 * @ngdoc directive
 * @name mdList
 * @module material.components.list
 *
 * @restrict E
 *
 * @description
 * The `<md-list>` directive is a list container for 1..n `<md-list-item>` tags.
 *
 * @usage
 * <hljs lang="html">
 * <md-list>
 *   <md-list-item class="md-2-line" ng-repeat="item in todos">
 *     <md-checkbox ng-model="item.done"></md-checkbox>
 *     <div class="md-list-item-text">
 *       <h3>{{item.title}}</h3>
 *       <p>{{item.description}}</p>
 *     </div>
 *   </md-list-item>
 * </md-list>
 * </hljs>
 */

function mdListDirective($mdTheming) {
  return {
    restrict: 'E',
    compile: function(tEl) {
      tEl[0].setAttribute('role', 'list');
      return $mdTheming;
    }
  };
}
/**
 * @ngdoc directive
 * @name mdListItem
 * @module material.components.list
 *
 * @restrict E
 *
 * @description
 * A `md-list-item` element can be used to represent some information in a row.<br/>
 *
 * @usage
 * ### Single Row Item
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *   </md-list-item>
 * </hljs>
 *
 * ### Multiple Lines
 * By using the following markup, you will be able to have two lines inside of one `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-2-line">
 *     <div class="md-list-item-text" layout="column">
 *       <p>First Line</p>
 *       <p>Second Line</p>
 *     </div>
 *   </md-list-item>
 * </hljs>
 *
 * It is also possible to have three lines inside of one list item.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-3-line">
 *     <div class="md-list-item-text" layout="column">
 *       <p>First Line</p>
 *       <p>Second Line</p>
 *       <p>Third Line</p>
 *     </div>
 *   </md-list-item>
 * </hljs>
 *
 * ### Secondary Items
 * Secondary items are elements which will be aligned at the end of the `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *     <md-button class="md-secondary">
 *       Secondary Button
 *     </md-button>
 *   </md-list-item>
 * </hljs>
 *
 * It also possible to have multiple secondary items inside of one `md-list-item`.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>Single Row Item</span>
 *     <md-button class="md-secondary">First Button</md-button>
 *     <md-button class="md-secondary">Second Button</md-button>
 *   </md-list-item>
 * </hljs>
 *
 * ### Proxy Item
 * Proxies are elements, which will execute their specific action on click<br/>
 * Currently supported proxy items are
 * - `md-checkbox` (Toggle)
 * - `md-switch` (Toggle)
 * - `md-menu` (Open)
 *
 * This means, when using a supported proxy item inside of `md-list-item`, the list item will
 * automatically become clickable and executes the associated action of the proxy element on click.
 *
 * It is possible to disable this behavior by applying the `md-no-proxy` class to the list item.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-no-proxy">
 *     <span>No Proxy List</span>
 *     <md-checkbox class="md-secondary"></md-checkbox>
 *   </md-list-item>
 * </hljs>
 *
 * Here are a few examples of proxy elements inside of a list item.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>First Line</span>
 *     <md-checkbox class="md-secondary"></md-checkbox>
 *   </md-list-item>
 * </hljs>
 *
 * The `md-checkbox` element will be automatically detected as a proxy element and will toggle on click.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <span>First Line</span>
 *     <md-switch class="md-secondary"></md-switch>
 *   </md-list-item>
 * </hljs>
 *
 * The recognized `md-switch` will toggle its state, when the user clicks on the `md-list-item`.
 *
 * It is also possible to have a `md-menu` inside of a `md-list-item`.
 * <hljs lang="html">
 *   <md-list-item>
 *     <p>Click anywhere to fire the secondary action</p>
 *     <md-menu class="md-secondary">
 *       <md-button class="md-icon-button">
 *         <md-icon md-svg-icon="communication:message"></md-icon>
 *       </md-button>
 *       <md-menu-content width="4">
 *         <md-menu-item>
 *           <md-button>
 *             Redial
 *           </md-button>
 *         </md-menu-item>
 *         <md-menu-item>
 *           <md-button>
 *             Check voicemail
 *           </md-button>
 *         </md-menu-item>
 *         <md-menu-divider></md-menu-divider>
 *         <md-menu-item>
 *           <md-button>
 *             Notifications
 *           </md-button>
 *         </md-menu-item>
 *       </md-menu-content>
 *     </md-menu>
 *   </md-list-item>
 * </hljs>
 *
 * The menu will automatically open, when the users clicks on the `md-list-item`.<br/>
 *
 * If the developer didn't specify any position mode on the menu, the `md-list-item` will automatically detect the
 * position mode and applies it to the `md-menu`.
 *
 * ### Avatars
 * Sometimes you may want to have some avatars inside of the `md-list-item `.<br/>
 * You are able to create a optimized icon for the list item, by applying the `.md-avatar` class on the `<img>` element.
 *
 * <hljs lang="html">
 *   <md-list-item>
 *     <img src="my-avatar.png" class="md-avatar">
 *     <span>Alan Turing</span>
 * </hljs>
 *
 * When using `<md-icon>` for an avatar, you have to use the `.md-avatar-icon` class.
 * <hljs lang="html">
 *   <md-list-item>
 *     <md-icon class="md-avatar-icon" md-svg-icon="avatars:timothy"></md-icon>
 *     <span>Timothy Kopra</span>
 *   </md-list-item>
 * </hljs>
 *
 * In cases, you have a `md-list-item`, which doesn't have any avatar,
 * but you want to align it with the other avatar items, you have to use the `.md-offset` class.
 *
 * <hljs lang="html">
 *   <md-list-item class="md-offset">
 *     <span>Jon Doe</span>
 *   </md-list-item>
 * </hljs>
 *
 * ### DOM modification
 * The `md-list-item` component automatically detects if the list item should be clickable.
 *
 * ---
 * If the `md-list-item` is clickable, we wrap all content inside of a `<div>` and create
 * an overlaying button, which will will execute the given actions (like `ng-href`, `ng-click`)
 *
 * We create an overlaying button, instead of wrapping all content inside of the button,
 * because otherwise some elements may not be clickable inside of the button.
 *
 * ---
 * When using a secondary item inside of your list item, the `md-list-item` component will automatically create
 * a secondary container at the end of the `md-list-item`, which contains all secondary items.
 *
 * The secondary item container is not static, because otherwise the overflow will not work properly on the
 * list item.
 *
 */
function mdListItemDirective($mdAria, $mdConstant, $mdUtil, $timeout) {
  var proxiedTypes = ['md-checkbox', 'md-switch', 'md-menu'];
  return {
    restrict: 'E',
    controller: 'MdListController',
    compile: function(tEl, tAttrs) {

      // Check for proxy controls (no ng-click on parent, and a control inside)
      var secondaryItems = tEl[0].querySelectorAll('.md-secondary');
      var hasProxiedElement;
      var proxyElement;
      var itemContainer = tEl;

      tEl[0].setAttribute('role', 'listitem');

      if (tAttrs.ngClick || tAttrs.ngDblclick ||  tAttrs.ngHref || tAttrs.href || tAttrs.uiSref || tAttrs.ngAttrUiSref) {
        wrapIn('button');
      } else if (!tEl.hasClass('md-no-proxy')) {

        for (var i = 0, type; type = proxiedTypes[i]; ++i) {
          if (proxyElement = tEl[0].querySelector(type)) {
            hasProxiedElement = true;
            break;
          }
        }

        if (hasProxiedElement) {
          wrapIn('div');
        } else {
          tEl.addClass('md-no-proxy');
        }

      }

      wrapSecondaryItems();
      setupToggleAria();

      if (hasProxiedElement && proxyElement.nodeName === "MD-MENU") {
        setupProxiedMenu();
      }

      function setupToggleAria() {
        var toggleTypes = ['md-switch', 'md-checkbox'];
        var toggle;

        for (var i = 0, toggleType; toggleType = toggleTypes[i]; ++i) {
          if (toggle = tEl.find(toggleType)[0]) {
            if (!toggle.hasAttribute('aria-label')) {
              var p = tEl.find('p')[0];
              if (!p) return;
              toggle.setAttribute('aria-label', 'Toggle ' + p.textContent);
            }
          }
        }
      }

      function setupProxiedMenu() {
        var menuEl = angular.element(proxyElement);

        var isEndAligned = menuEl.parent().hasClass('md-secondary-container') ||
                           proxyElement.parentNode.firstElementChild !== proxyElement;

        var xAxisPosition = 'left';

        if (isEndAligned) {
          // When the proxy item is aligned at the end of the list, we have to set the origin to the end.
          xAxisPosition = 'right';
        }

        // Set the position mode / origin of the proxied menu.
        if (!menuEl.attr('md-position-mode')) {
          menuEl.attr('md-position-mode', xAxisPosition + ' target');
        }

        // Apply menu open binding to menu button
        var menuOpenButton = menuEl.children().eq(0);
        if (!hasClickEvent(menuOpenButton[0])) {
          menuOpenButton.attr('ng-click', '$mdMenu.open($event)');
        }

        if (!menuOpenButton.attr('aria-label')) {
          menuOpenButton.attr('aria-label', 'Open List Menu');
        }
      }

      function wrapIn(type) {
        if (type == 'div') {
          itemContainer = angular.element('<div class="md-no-style md-list-item-inner">');
          itemContainer.append(tEl.contents());
          tEl.addClass('md-proxy-focus');
        } else {
          // Element which holds the default list-item content.
          itemContainer = angular.element(
            '<div class="md-button md-no-style">'+
            '   <div class="md-list-item-inner"></div>'+
            '</div>'
          );

          // Button which shows ripple and executes primary action.
          var buttonWrap = angular.element(
            '<md-button class="md-no-style"></md-button>'
          );

          copyAttributes(tEl[0], buttonWrap[0]);

          // If there is no aria-label set on the button (previously copied over if present)
          // we determine the label from the content and copy it to the button.
          if (!buttonWrap.attr('aria-label')) {
            buttonWrap.attr('aria-label', $mdAria.getText(tEl));
          }

          // We allow developers to specify the `md-no-focus` class, to disable the focus style
          // on the button executor. Once more classes should be forwarded, we should probably make the
          // class forward more generic.
          if (tEl.hasClass('md-no-focus')) {
            buttonWrap.addClass('md-no-focus');
          }

          // Append the button wrap before our list-item content, because it will overlay in relative.
          itemContainer.prepend(buttonWrap);
          itemContainer.children().eq(1).append(tEl.contents());

          tEl.addClass('_md-button-wrap');
        }

        tEl[0].setAttribute('tabindex', '-1');
        tEl.append(itemContainer);
      }

      function wrapSecondaryItems() {
        var secondaryItemsWrapper = angular.element('<div class="md-secondary-container">');

        angular.forEach(secondaryItems, function(secondaryItem) {
          wrapSecondaryItem(secondaryItem, secondaryItemsWrapper);
        });

        itemContainer.append(secondaryItemsWrapper);
      }

      function wrapSecondaryItem(secondaryItem, container) {
        // If the current secondary item is not a button, but contains a ng-click attribute,
        // the secondary item will be automatically wrapped inside of a button.
        if (secondaryItem && !isButton(secondaryItem) && secondaryItem.hasAttribute('ng-click')) {

          $mdAria.expect(secondaryItem, 'aria-label');
          var buttonWrapper = angular.element('<md-button class="md-secondary md-icon-button">');

          // Copy the attributes from the secondary item to the generated button.
          // We also support some additional attributes from the secondary item,
          // because some developers may use a ngIf, ngHide, ngShow on their item.
          copyAttributes(secondaryItem, buttonWrapper[0], ['ng-if', 'ng-hide', 'ng-show']);

          secondaryItem.setAttribute('tabindex', '-1');
          buttonWrapper.append(secondaryItem);

          secondaryItem = buttonWrapper[0];
        }

        if (secondaryItem && (!hasClickEvent(secondaryItem) || (!tAttrs.ngClick && isProxiedElement(secondaryItem)))) {
          // In this case we remove the secondary class, so we can identify it later, when we searching for the
          // proxy items.
          angular.element(secondaryItem).removeClass('md-secondary');
        }

        tEl.addClass('md-with-secondary');
        container.append(secondaryItem);
      }

      /**
       * Copies attributes from a source element to the destination element
       * By default the function will copy the most necessary attributes, supported
       * by the button executor for clickable list items.
       * @param source Element with the specified attributes
       * @param destination Element which will retrieve the attributes
       * @param extraAttrs Additional attributes, which will be copied over.
       */
      function copyAttributes(source, destination, extraAttrs) {
        var copiedAttrs = $mdUtil.prefixer([
          'ng-if', 'ng-click', 'ng-dblclick', 'aria-label', 'ng-disabled', 'ui-sref',
          'href', 'ng-href', 'rel', 'target', 'ng-attr-ui-sref', 'ui-sref-opts'
        ]);

        if (extraAttrs) {
          copiedAttrs = copiedAttrs.concat($mdUtil.prefixer(extraAttrs));
        }

        angular.forEach(copiedAttrs, function(attr) {
          if (source.hasAttribute(attr)) {
            destination.setAttribute(attr, source.getAttribute(attr));
            source.removeAttribute(attr);
          }
        });
      }

      function isProxiedElement(el) {
        return proxiedTypes.indexOf(el.nodeName.toLowerCase()) != -1;
      }

      function isButton(el) {
        var nodeName = el.nodeName.toUpperCase();

        return nodeName == "MD-BUTTON" || nodeName == "BUTTON";
      }

      function hasClickEvent (element) {
        var attr = element.attributes;
        for (var i = 0; i < attr.length; i++) {
          if (tAttrs.$normalize(attr[i].name) === 'ngClick') return true;
        }
        return false;
      }

      return postLink;

      function postLink($scope, $element, $attr, ctrl) {
        $element.addClass('_md');     // private md component indicator for styling

        var proxies       = [],
            firstElement  = $element[0].firstElementChild,
            isButtonWrap  = $element.hasClass('_md-button-wrap'),
            clickChild    = isButtonWrap ? firstElement.firstElementChild : firstElement,
            hasClick      = clickChild && hasClickEvent(clickChild),
            noProxies     = $element.hasClass('md-no-proxy');

        computeProxies();
        computeClickable();

        if (proxies.length) {
          angular.forEach(proxies, function(proxy) {
            proxy = angular.element(proxy);

            $scope.mouseActive = false;
            proxy.on('mousedown', function() {
              $scope.mouseActive = true;
              $timeout(function(){
                $scope.mouseActive = false;
              }, 100);
            })
            .on('focus', function() {
              if ($scope.mouseActive === false) { $element.addClass('md-focused'); }
              proxy.on('blur', function proxyOnBlur() {
                $element.removeClass('md-focused');
                proxy.off('blur', proxyOnBlur);
              });
            });
          });
        }


        function computeProxies() {

          if (firstElement && firstElement.children && !hasClick && !noProxies) {

            angular.forEach(proxiedTypes, function(type) {

              // All elements which are not capable for being used a proxy have the .md-secondary class
              // applied. These items had been sorted out in the secondary wrap function.
              angular.forEach(firstElement.querySelectorAll(type + ':not(.md-secondary)'), function(child) {
                proxies.push(child);
              });
            });

          }
        }

        function computeClickable() {
          if (proxies.length == 1 || hasClick) {
            $element.addClass('md-clickable');

            if (!hasClick) {
              ctrl.attachRipple($scope, angular.element($element[0].querySelector('.md-no-style')));
            }
          }
        }

        function isEventFromControl(event) {
          var forbiddenControls = ['md-slider'];

          // If there is no path property in the event, then we can assume that the event was not bubbled.
          if (!event.path) {
            return forbiddenControls.indexOf(event.target.tagName.toLowerCase()) !== -1;
          }

          // We iterate the event path up and check for a possible component.
          // Our maximum index to search, is the list item root.
          var maxPath = event.path.indexOf($element.children()[0]);

          for (var i = 0; i < maxPath; i++) {
            if (forbiddenControls.indexOf(event.path[i].tagName.toLowerCase()) !== -1) {
              return true;
            }
          }
        }

        var clickChildKeypressListener = function(e) {
          if (e.target.nodeName != 'INPUT' && e.target.nodeName != 'TEXTAREA' && !e.target.isContentEditable) {
            var keyCode = e.which || e.keyCode;
            if (keyCode == $mdConstant.KEY_CODE.SPACE) {
              if (clickChild) {
                clickChild.click();
                e.preventDefault();
                e.stopPropagation();
              }
            }
          }
        };

        if (!hasClick && !proxies.length) {
          clickChild && clickChild.addEventListener('keypress', clickChildKeypressListener);
        }

        $element.off('click');
        $element.off('keypress');

        if (proxies.length == 1 && clickChild) {
          $element.children().eq(0).on('click', function(e) {
            // When the event is coming from an control and it should not trigger the proxied element
            // then we are skipping.
            if (isEventFromControl(e)) return;

            var parentButton = $mdUtil.getClosest(e.target, 'BUTTON');
            if (!parentButton && clickChild.contains(e.target)) {
              angular.forEach(proxies, function(proxy) {
                if (e.target !== proxy && !proxy.contains(e.target)) {
                  if (proxy.nodeName === 'MD-MENU') {
                    proxy = proxy.children[0];
                  }
                  angular.element(proxy).triggerHandler('click');
                }
              });
            }
          });
        }

        $scope.$on('$destroy', function () {
          clickChild && clickChild.removeEventListener('keypress', clickChildKeypressListener);
        });
      }
    }
  };
}

/*
 * @private
 * @ngdoc controller
 * @name MdListController
 * @module material.components.list
 *
 */
function MdListController($scope, $element, $mdListInkRipple) {
  var ctrl = this;
  ctrl.attachRipple = attachRipple;

  function attachRipple (scope, element) {
    var options = {};
    $mdListInkRipple.attach(scope, element, options);
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.menu
 */

angular.module('material.components.menu', [
  'material.core',
  'material.components.backdrop'
]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.menuBar
 */

angular.module('material.components.menuBar', [
  'material.core',
  'material.components.icon',
  'material.components.menu'
]);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.navBar
 */


MdNavBarController.$inject = ["$element", "$scope", "$timeout", "$mdConstant"];
MdNavItem.$inject = ["$mdAria", "$$rAF"];
MdNavItemController.$inject = ["$element"];
MdNavBar.$inject = ["$mdAria", "$mdTheming"];
angular.module('material.components.navBar', ['material.core'])
    .controller('MdNavBarController', MdNavBarController)
    .directive('mdNavBar', MdNavBar)
    .controller('MdNavItemController', MdNavItemController)
    .directive('mdNavItem', MdNavItem);


/*****************************************************************************
 *                            PUBLIC DOCUMENTATION                           *
 *****************************************************************************/
/**
 * @ngdoc directive
 * @name mdNavBar
 * @module material.components.navBar
 *
 * @restrict E
 *
 * @description
 * The `<md-nav-bar>` directive renders a list of material tabs that can be used
 * for top-level page navigation. Unlike `<md-tabs>`, it has no concept of a tab
 * body and no bar pagination.
 *
 * Because it deals with page navigation, certain routing concepts are built-in.
 * Route changes via via ng-href, ui-sref, or ng-click events are supported.
 * Alternatively, the user could simply watch currentNavItem for changes.
 *
 * Accessibility functionality is implemented as a site navigator with a
 * listbox, according to
 * https://www.w3.org/TR/wai-aria-practices/#Site_Navigator_Tabbed_Style
 *
 * @param {string=} mdSelectedNavItem The name of the current tab; this must
 *     match the name attribute of `<md-nav-item>`
 * @param {boolean=} mdNoInkBar If set to true, the ink bar will be hidden.
 * @param {string=} navBarAriaLabel An aria-label for the nav-bar
 *
 * @usage
 * <hljs lang="html">
 *  <md-nav-bar md-selected-nav-item="currentNavItem">
 *    <md-nav-item md-nav-click="goto('page1')" name="page1">
 *      Page One
 *    </md-nav-item>
 *    <md-nav-item md-nav-href="#page2" name="page3">Page Two</md-nav-item>
 *    <md-nav-item md-nav-sref="page3" name="page2">Page Three</md-nav-item>
 *    <md-nav-item
 *      md-nav-sref="app.page4"
 *      sref-opts="{reload: true, notify: true}"
 *      name="page4">
 *      Page Four
 *    </md-nav-item>
 *  </md-nav-bar>
 *</hljs>
 * <hljs lang="js">
 * (function() {
 *   'use strict';
 *
 *    $rootScope.$on('$routeChangeSuccess', function(event, current) {
 *      $scope.currentLink = getCurrentLinkFromRoute(current);
 *    });
 * });
 * </hljs>
 */

/*****************************************************************************
 *                            mdNavItem
 *****************************************************************************/
/**
 * @ngdoc directive
 * @name mdNavItem
 * @module material.components.navBar
 *
 * @restrict E
 *
 * @description
 * `<md-nav-item>` describes a page navigation link within the `<md-nav-bar>`
 * component. It renders an md-button as the actual link.
 *
 * Exactly one of the mdNavClick, mdNavHref, mdNavSref attributes are required
 * to be specified.
 *
 * @param {Function=} mdNavClick Function which will be called when the
 *     link is clicked to change the page. Renders as an `ng-click`.
 * @param {string=} mdNavHref url to transition to when this link is clicked.
 *     Renders as an `ng-href`.
 * @param {string=} mdNavSref Ui-router state to transition to when this link is
 *     clicked. Renders as a `ui-sref`.
 * @param {!Object=} srefOpts Ui-router options that are passed to the
 *     `$state.go()` function. See the [Ui-router documentation for details]
 *     (https://ui-router.github.io/docs/latest/interfaces/transition.transitionoptions.html).
 * @param {string=} name The name of this link. Used by the nav bar to know
 *     which link is currently selected.
 * @param {string=} aria-label Adds alternative text for accessibility
 *
 * @usage
 * See `<md-nav-bar>` for usage.
 */


/*****************************************************************************
 *                                IMPLEMENTATION                             *
 *****************************************************************************/

function MdNavBar($mdAria, $mdTheming) {
  return {
    restrict: 'E',
    transclude: true,
    controller: MdNavBarController,
    controllerAs: 'ctrl',
    bindToController: true,
    scope: {
      'mdSelectedNavItem': '=?',
      'mdNoInkBar': '=?',
      'navBarAriaLabel': '@?',
    },
    template:
      '<div class="md-nav-bar">' +
        '<nav role="navigation">' +
          '<ul class="_md-nav-bar-list" ng-transclude role="listbox"' +
            'tabindex="0"' +
            'ng-focus="ctrl.onFocus()"' +
            'ng-keydown="ctrl.onKeydown($event)"' +
            'aria-label="{{ctrl.navBarAriaLabel}}">' +
          '</ul>' +
        '</nav>' +
        '<md-nav-ink-bar ng-hide="ctrl.mdNoInkBar"></md-nav-ink-bar>' +
      '</div>',
    link: function(scope, element, attrs, ctrl) {
      $mdTheming(element);
      if (!ctrl.navBarAriaLabel) {
        $mdAria.expectAsync(element, 'aria-label', angular.noop);
      }
    },
  };
}

/**
 * Controller for the nav-bar component.
 *
 * Accessibility functionality is implemented as a site navigator with a
 * listbox, according to
 * https://www.w3.org/TR/wai-aria-practices/#Site_Navigator_Tabbed_Style
 * @param {!angular.JQLite} $element
 * @param {!angular.Scope} $scope
 * @param {!angular.Timeout} $timeout
 * @param {!Object} $mdConstant
 * @constructor
 * @final
 * @ngInject
 */
function MdNavBarController($element, $scope, $timeout, $mdConstant) {
  // Injected variables
  /** @private @const {!angular.Timeout} */
  this._$timeout = $timeout;

  /** @private @const {!angular.Scope} */
  this._$scope = $scope;

  /** @private @const {!Object} */
  this._$mdConstant = $mdConstant;

  // Data-bound variables.
  /** @type {string} */
  this.mdSelectedNavItem;

  /** @type {string} */
  this.navBarAriaLabel;

  // State variables.

  /** @type {?angular.JQLite} */
  this._navBarEl = $element[0];

  /** @type {?angular.JQLite} */
  this._inkbar;

  var self = this;
  // need to wait for transcluded content to be available
  var deregisterTabWatch = this._$scope.$watch(function() {
    return self._navBarEl.querySelectorAll('._md-nav-button').length;
  },
  function(newLength) {
    if (newLength > 0) {
      self._initTabs();
      deregisterTabWatch();
    }
  });
}



/**
 * Initializes the tab components once they exist.
 * @private
 */
MdNavBarController.prototype._initTabs = function() {
  this._inkbar = angular.element(this._navBarEl.querySelector('md-nav-ink-bar'));

  var self = this;
  this._$timeout(function() {
    self._updateTabs(self.mdSelectedNavItem, undefined);
  });

  this._$scope.$watch('ctrl.mdSelectedNavItem', function(newValue, oldValue) {
    // Wait a digest before update tabs for products doing
    // anything dynamic in the template.
    self._$timeout(function() {
      self._updateTabs(newValue, oldValue);
    });
  });
};

/**
 * Set the current tab to be selected.
 * @param {string|undefined} newValue New current tab name.
 * @param {string|undefined} oldValue Previous tab name.
 * @private
 */
MdNavBarController.prototype._updateTabs = function(newValue, oldValue) {
  var self = this;
  var tabs = this._getTabs();

  // this._getTabs can return null if nav-bar has not yet been initialized
  if(!tabs)
    return;

  var oldIndex = -1;
  var newIndex = -1;
  var newTab = this._getTabByName(newValue);
  var oldTab = this._getTabByName(oldValue);

  if (oldTab) {
    oldTab.setSelected(false);
    oldIndex = tabs.indexOf(oldTab);
  }

  if (newTab) {
    newTab.setSelected(true);
    newIndex = tabs.indexOf(newTab);
  }

  this._$timeout(function() {
    self._updateInkBarStyles(newTab, newIndex, oldIndex);
  });
};

/**
 * Repositions the ink bar to the selected tab.
 * @private
 */
MdNavBarController.prototype._updateInkBarStyles = function(tab, newIndex, oldIndex) {
  this._inkbar.toggleClass('_md-left', newIndex < oldIndex)
      .toggleClass('_md-right', newIndex > oldIndex);

  this._inkbar.css({display: newIndex < 0 ? 'none' : ''});

  if (tab) {
    var tabEl = tab.getButtonEl();
    var left = tabEl.offsetLeft;

    this._inkbar.css({left: left + 'px', width: tabEl.offsetWidth + 'px'});
  }
};

/**
 * Returns an array of the current tabs.
 * @return {!Array<!NavItemController>}
 * @private
 */
MdNavBarController.prototype._getTabs = function() {
  var controllers = Array.prototype.slice.call(
    this._navBarEl.querySelectorAll('.md-nav-item'))
    .map(function(el) {
      return angular.element(el).controller('mdNavItem')
    });
  return controllers.indexOf(undefined) ? controllers : null;
};

/**
 * Returns the tab with the specified name.
 * @param {string} name The name of the tab, found in its name attribute.
 * @return {!NavItemController|undefined}
 * @private
 */
MdNavBarController.prototype._getTabByName = function(name) {
  return this._findTab(function(tab) {
    return tab.getName() == name;
  });
};

/**
 * Returns the selected tab.
 * @return {!NavItemController|undefined}
 * @private
 */
MdNavBarController.prototype._getSelectedTab = function() {
  return this._findTab(function(tab) {
    return tab.isSelected();
  });
};

/**
 * Returns the focused tab.
 * @return {!NavItemController|undefined}
 */
MdNavBarController.prototype.getFocusedTab = function() {
  return this._findTab(function(tab) {
    return tab.hasFocus();
  });
};

/**
 * Find a tab that matches the specified function.
 * @private
 */
MdNavBarController.prototype._findTab = function(fn) {
  var tabs = this._getTabs();
  for (var i = 0; i < tabs.length; i++) {
    if (fn(tabs[i])) {
      return tabs[i];
    }
  }

  return null;
};

/**
 * Direct focus to the selected tab when focus enters the nav bar.
 */
MdNavBarController.prototype.onFocus = function() {
  var tab = this._getSelectedTab();
  if (tab) {
    tab.setFocused(true);
  }
};

/**
 * Move focus from oldTab to newTab.
 * @param {!NavItemController} oldTab
 * @param {!NavItemController} newTab
 * @private
 */
MdNavBarController.prototype._moveFocus = function(oldTab, newTab) {
  oldTab.setFocused(false);
  newTab.setFocused(true);
};

/**
 * Responds to keypress events.
 * @param {!Event} e
 */
MdNavBarController.prototype.onKeydown = function(e) {
  var keyCodes = this._$mdConstant.KEY_CODE;
  var tabs = this._getTabs();
  var focusedTab = this.getFocusedTab();
  if (!focusedTab) return;

  var focusedTabIndex = tabs.indexOf(focusedTab);

  // use arrow keys to navigate between tabs
  switch (e.keyCode) {
    case keyCodes.UP_ARROW:
    case keyCodes.LEFT_ARROW:
      if (focusedTabIndex > 0) {
        this._moveFocus(focusedTab, tabs[focusedTabIndex - 1]);
      }
      break;
    case keyCodes.DOWN_ARROW:
    case keyCodes.RIGHT_ARROW:
      if (focusedTabIndex < tabs.length - 1) {
        this._moveFocus(focusedTab, tabs[focusedTabIndex + 1]);
      }
      break;
    case keyCodes.SPACE:
    case keyCodes.ENTER:
      // timeout to avoid a "digest already in progress" console error
      this._$timeout(function() {
        focusedTab.getButtonEl().click();
      });
      break;
  }
};

/**
 * @ngInject
 */
function MdNavItem($mdAria, $$rAF) {
  return {
    restrict: 'E',
    require: ['mdNavItem', '^mdNavBar'],
    controller: MdNavItemController,
    bindToController: true,
    controllerAs: 'ctrl',
    replace: true,
    transclude: true,
    template: function(tElement, tAttrs) {
      var hasNavClick = tAttrs.mdNavClick;
      var hasNavHref = tAttrs.mdNavHref;
      var hasNavSref = tAttrs.mdNavSref;
      var hasSrefOpts = tAttrs.srefOpts;
      var navigationAttribute;
      var navigationOptions;
      var buttonTemplate;

      // Cannot specify more than one nav attribute
      if ((hasNavClick ? 1:0) + (hasNavHref ? 1:0) + (hasNavSref ? 1:0) > 1) {
        throw Error(
          'Must not specify more than one of the md-nav-click, md-nav-href, ' +
          'or md-nav-sref attributes per nav-item directive.'
        );
      }

      if (hasNavClick) {
        navigationAttribute = 'ng-click="ctrl.mdNavClick()"';
      } else if (hasNavHref) {
        navigationAttribute = 'ng-href="{{ctrl.mdNavHref}}"';
      } else if (hasNavSref) {
        navigationAttribute = 'ui-sref="{{ctrl.mdNavSref}}"';
      }

      navigationOptions = hasSrefOpts ? 'ui-sref-opts="{{ctrl.srefOpts}}" ' : '';

      if (navigationAttribute) {
        buttonTemplate = '' +
          '<md-button class="_md-nav-button md-accent" ' +
            'ng-class="ctrl.getNgClassMap()" ' +
            'ng-blur="ctrl.setFocused(false)" ' +
            'tabindex="-1" ' +
            navigationOptions +
            navigationAttribute + '>' +
            '<span ng-transclude class="_md-nav-button-text"></span>' +
          '</md-button>';
      }

      return '' +
        '<li class="md-nav-item" ' +
          'role="option" ' +
          'aria-selected="{{ctrl.isSelected()}}">' +
          (buttonTemplate || '') +
        '</li>';
    },
    scope: {
      'mdNavClick': '&?',
      'mdNavHref': '@?',
      'mdNavSref': '@?',
      'srefOpts': '=?',
      'name': '@',
    },
    link: function(scope, element, attrs, controllers) {
      // When accessing the element's contents synchronously, they
      // may not be defined yet because of transclusion. There is a higher
      // chance that it will be accessible if we wait one frame.
      $$rAF(function() {
        var mdNavItem = controllers[0];
        var mdNavBar = controllers[1];
        var navButton = angular.element(element[0].querySelector('._md-nav-button'));

        if (!mdNavItem.name) {
          mdNavItem.name = angular.element(element[0]
              .querySelector('._md-nav-button-text')).text().trim();
        }

        navButton.on('click', function() {
          mdNavBar.mdSelectedNavItem = mdNavItem.name;
          scope.$apply();
        });

        $mdAria.expectWithText(element, 'aria-label');
      });
    }
  };
}

/**
 * Controller for the nav-item component.
 * @param {!angular.JQLite} $element
 * @constructor
 * @final
 * @ngInject
 */
function MdNavItemController($element) {

  /** @private @const {!angular.JQLite} */
  this._$element = $element;

  // Data-bound variables

  /** @const {?Function} */
  this.mdNavClick;

  /** @const {?string} */
  this.mdNavHref;

  /** @const {?string} */
  this.mdNavSref;
  /** @const {?Object} */
  this.srefOpts;
  /** @const {?string} */
  this.name;

  // State variables
  /** @private {boolean} */
  this._selected = false;

  /** @private {boolean} */
  this._focused = false;
}

/**
 * Returns a map of class names and values for use by ng-class.
 * @return {!Object<string,boolean>}
 */
MdNavItemController.prototype.getNgClassMap = function() {
  return {
    'md-active': this._selected,
    'md-primary': this._selected,
    'md-unselected': !this._selected,
    'md-focused': this._focused,
  };
};

/**
 * Get the name attribute of the tab.
 * @return {string}
 */
MdNavItemController.prototype.getName = function() {
  return this.name;
};

/**
 * Get the button element associated with the tab.
 * @return {!Element}
 */
MdNavItemController.prototype.getButtonEl = function() {
  return this._$element[0].querySelector('._md-nav-button');
};

/**
 * Set the selected state of the tab.
 * @param {boolean} isSelected
 */
MdNavItemController.prototype.setSelected = function(isSelected) {
  this._selected = isSelected;
};

/**
 * @return {boolean}
 */
MdNavItemController.prototype.isSelected = function() {
  return this._selected;
};

/**
 * Set the focused state of the tab.
 * @param {boolean} isFocused
 */
MdNavItemController.prototype.setFocused = function(isFocused) {
  this._focused = isFocused;

  if (isFocused) {
    this.getButtonEl().focus();
  }
};

/**
 * @return {boolean}
 */
MdNavItemController.prototype.hasFocus = function() {
  return this._focused;
};

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.panel
 */
MdPanelService.$inject = ["presets", "$rootElement", "$rootScope", "$injector", "$window"];
angular
  .module('material.components.panel', [
    'material.core',
    'material.components.backdrop'
  ])
  .provider('$mdPanel', MdPanelProvider);


/*****************************************************************************
 *                            PUBLIC DOCUMENTATION                           *
 *****************************************************************************/


/**
 * @ngdoc service
 * @name $mdPanelProvider
 * @module material.components.panel
 *
 * @description
 * `$mdPanelProvider` allows users to create configuration presets that will be
 * stored within a cached presets object. When the configuration is needed, the
 * user can request the preset by passing it as the first parameter in the
 * `$mdPanel.create` or `$mdPanel.open` methods.
 *
 * @usage
 * <hljs lang="js">
 * (function(angular, undefined) {
 *   'use strict';
 *
 *   angular
 *       .module('demoApp', ['ngMaterial'])
 *       .config(DemoConfig)
 *       .controller('DemoCtrl', DemoCtrl)
 *       .controller('DemoMenuCtrl', DemoMenuCtrl);
 *
 *   function DemoConfig($mdPanelProvider) {
 *     $mdPanelProvider.definePreset('demoPreset', {
 *       attachTo: angular.element(document.body),
 *       controller: DemoMenuCtrl,
 *       controllerAs: 'ctrl',
 *       template: '' +
 *           '<div class="menu-panel" md-whiteframe="4">' +
 *           '  <div class="menu-content">' +
 *           '    <div class="menu-item" ng-repeat="item in ctrl.items">' +
 *           '      <button class="md-button">' +
 *           '        <span>{{item}}</span>' +
 *           '      </button>' +
 *           '    </div>' +
 *           '    <md-divider></md-divider>' +
 *           '    <div class="menu-item">' +
 *           '      <button class="md-button" ng-click="ctrl.closeMenu()">' +
 *           '        <span>Close Menu</span>' +
 *           '      </button>' +
 *           '    </div>' +
 *           '  </div>' +
 *           '</div>',
 *       panelClass: 'menu-panel-container',
 *       focusOnOpen: false,
 *       zIndex: 100,
 *       propagateContainerEvents: true,
 *       groupName: 'menus'
 *     });
 *   }
 *
 *   function PanelProviderCtrl($mdPanel) {
 *     this.navigation = {
 *       name: 'navigation',
 *       items: [
 *         'Home',
 *         'About',
 *         'Contact'
 *       ]
 *     };
 *     this.favorites = {
 *       name: 'favorites',
 *       items: [
 *         'Add to Favorites'
 *       ]
 *     };
 *     this.more = {
 *       name: 'more',
 *       items: [
 *         'Account',
 *         'Sign Out'
 *       ]
 *     };
 *
 *     $mdPanel.newPanelGroup('menus', {
 *       maxOpen: 2
 *     });
 *
 *     this.showMenu = function($event, menu) {
 *       $mdPanel.open('demoPreset', {
 *         id: 'menu_' + menu.name,
 *         position: $mdPanel.newPanelPosition()
 *             .relativeTo($event.srcElement)
 *             .addPanelPosition(
 *               $mdPanel.xPosition.ALIGN_START,
 *               $mdPanel.yPosition.BELOW
 *             ),
 *         locals: {
 *           items: menu.items
 *         },
 *         openFrom: $event
 *       });
 *     };
 *   }
 *
 *   function PanelMenuCtrl(mdPanelRef) {
 *     // The controller is provided with an import named 'mdPanelRef'
 *     this.closeMenu = function() {
 *       mdPanelRef && mdPanelRef.close();
 *     };
 *   }
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdPanelProvider#definePreset
 * @description
 * Takes the passed in preset name and preset configuration object and adds it
 * to the `_presets` object of the provider. This `_presets` object is then
 * passed along to the `$mdPanel` service.
 *
 * @param {string} name Preset name.
 * @param {!Object} preset Specific configuration object that can contain any
 *     and all of the parameters avaialble within the `$mdPanel.create` method.
 *     However, parameters that pertain to id, position, animation, and user
 *     interaction are not allowed and will be removed from the preset
 *     configuration.
 */


/*****************************************************************************
 *                               MdPanel Service                             *
 *****************************************************************************/


/**
 * @ngdoc service
 * @name $mdPanel
 * @module material.components.panel
 *
 * @description
 * `$mdPanel` is a robust, low-level service for creating floating panels on
 * the screen. It can be used to implement tooltips, dialogs, pop-ups, etc.
 *
 * @usage
 * <hljs lang="js">
 * (function(angular, undefined) {
 *   'use strict';
 *
 *   angular
 *       .module('demoApp', ['ngMaterial'])
 *       .controller('DemoDialogController', DialogController);
 *
 *   var panelRef;
 *
 *   function showPanel($event) {
 *     var panelPosition = $mdPanel.newPanelPosition()
 *         .absolute()
 *         .top('50%')
 *         .left('50%');
 *
 *     var panelAnimation = $mdPanel.newPanelAnimation()
 *         .targetEvent($event)
 *         .defaultAnimation('md-panel-animate-fly')
 *         .closeTo('.show-button');
 *
 *     var config = {
 *       attachTo: angular.element(document.body),
 *       controller: DialogController,
 *       controllerAs: 'ctrl',
 *       position: panelPosition,
 *       animation: panelAnimation,
 *       targetEvent: $event,
 *       templateUrl: 'dialog-template.html',
 *       clickOutsideToClose: true,
 *       escapeToClose: true,
 *       focusOnOpen: true
 *     };
 *
 *     $mdPanel.open(config)
 *         .then(function(result) {
 *           panelRef = result;
 *         });
 *   }
 *
 *   function DialogController(MdPanelRef) {
 *     function closeDialog() {
 *       if (MdPanelRef) MdPanelRef.close();
 *     }
 *   }
 * })(angular);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name $mdPanel#create
 * @description
 * Creates a panel with the specified options.
 *
 * @param config {!Object=} Specific configuration object that may contain the
 *     following properties:
 *
 *   - `id` - `{string=}`: An ID to track the panel by. When an ID is provided,
 *     the created panel is added to a tracked panels object. Any subsequent
 *     requests made to create a panel with that ID are ignored. This is useful
 *     in having the panel service not open multiple panels from the same user
 *     interaction when there is no backdrop and events are propagated. Defaults
 *     to an arbitrary string that is not tracked.
 *   - `template` - `{string=}`: HTML template to show in the panel. This
 *     **must** be trusted HTML with respect to AngularJS’s
 *     [$sce service](https://docs.angularjs.org/api/ng/service/$sce).
 *   - `templateUrl` - `{string=}`: The URL that will be used as the content of
 *     the panel.
 *   - `contentElement` - `{(string|!angular.JQLite|!Element)=}`: Pre-compiled
 *     element to be used as the panel's content.
 *   - `controller` - `{(function|string)=}`: The controller to associate with
 *     the panel. The controller can inject a reference to the returned
 *     panelRef, which allows the panel to be closed, hidden, and shown. Any
 *     fields passed in through locals or resolve will be bound to the
 *     controller.
 *   - `controllerAs` - `{string=}`: An alias to assign the controller to on
 *     the scope.
 *   - `bindToController` - `{boolean=}`: Binds locals to the controller
 *     instead of passing them in. Defaults to true, as this is a best
 *     practice.
 *   - `locals` - `{Object=}`: An object containing key/value pairs. The keys
 *     will be used as names of values to inject into the controller. For
 *     example, `locals: {three: 3}` would inject `three` into the controller,
 *     with the value 3. 'mdPanelRef' is a reserved key, and will always 
 *     be set to the created MdPanelRef instance.
 *   - `resolve` - `{Object=}`: Similar to locals, except it takes promises as
 *     values. The panel will not open until all of the promises resolve.
 *   - `attachTo` - `{(string|!angular.JQLite|!Element)=}`: The element to
 *     attach the panel to. Defaults to appending to the root element of the
 *     application.
 *   - `propagateContainerEvents` - `{boolean=}`: Whether pointer or touch
 *     events should be allowed to propagate 'go through' the container, aka the
 *     wrapper, of the panel. Defaults to false.
 *   - `panelClass` - `{string=}`: A css class to apply to the panel element.
 *     This class should define any borders, box-shadow, etc. for the panel.
 *   - `zIndex` - `{number=}`: The z-index to place the panel at.
 *     Defaults to 80.
 *   - `position` - `{MdPanelPosition=}`: An MdPanelPosition object that
 *     specifies the alignment of the panel. For more information, see
 *     `MdPanelPosition`.
 *   - `clickOutsideToClose` - `{boolean=}`: Whether the user can click
 *     outside the panel to close it. Defaults to false.
 *   - `escapeToClose` - `{boolean=}`: Whether the user can press escape to
 *     close the panel. Defaults to false.
 *   - `onCloseSuccess` - `{function(!panelRef, string)=}`: Function that is
 *     called after the close successfully finishes. The first parameter passed
 *     into this function is the current panelRef and the 2nd is an optional
 *     string explaining the close reason. The currently supported closeReasons
 *     can be found in the MdPanelRef.closeReasons enum. These are by default
 *     passed along by the panel.
 *   - `trapFocus` - `{boolean=}`: Whether focus should be trapped within the
 *     panel. If `trapFocus` is true, the user will not be able to interact
 *     with the rest of the page until the panel is dismissed. Defaults to
 *     false.
 *   - `focusOnOpen` - `{boolean=}`: An option to override focus behavior on
 *     open. Only disable if focusing some other way, as focus management is
 *     required for panels to be accessible. Defaults to true.
 *   - `fullscreen` - `{boolean=}`: Whether the panel should be full screen.
 *     Applies the class `._md-panel-fullscreen` to the panel on open. Defaults
 *     to false.
 *   - `animation` - `{MdPanelAnimation=}`: An MdPanelAnimation object that
 *     specifies the animation of the panel. For more information, see
 *     `MdPanelAnimation`.
 *   - `hasBackdrop` - `{boolean=}`: Whether there should be an opaque backdrop
 *     behind the panel. Defaults to false.
 *   - `disableParentScroll` - `{boolean=}`: Whether the user can scroll the
 *     page behind the panel. Defaults to false.
 *   - `onDomAdded` - `{function=}`: Callback function used to announce when
 *     the panel is added to the DOM.
 *   - `onOpenComplete` - `{function=}`: Callback function used to announce
 *     when the open() action is finished.
 *   - `onRemoving` - `{function=}`: Callback function used to announce the
 *     close/hide() action is starting.
 *   - `onDomRemoved` - `{function=}`: Callback function used to announce when
 *     the panel is removed from the DOM.
 *   - `origin` - `{(string|!angular.JQLite|!Element)=}`: The element to focus
 *     on when the panel closes. This is commonly the element which triggered
 *     the opening of the panel. If you do not use `origin`, you need to control
 *     the focus manually.
 *   - `groupName` - `{(string|!Array<string>)=}`: A group name or an array of
 *     group names. The group name is used for creating a group of panels. The
 *     group is used for configuring the number of open panels and identifying
 *     specific behaviors for groups. For instance, all tooltips could be
 *     identified using the same groupName.
 *
 * @returns {!MdPanelRef} panelRef
 */

/**
 * @ngdoc method
 * @name $mdPanel#open
 * @description
 * Calls the create method above, then opens the panel. This is a shortcut for
 * creating and then calling open manually. If custom methods need to be
 * called when the panel is added to the DOM or opened, do not use this method.
 * Instead create the panel, chain promises on the domAdded and openComplete
 * methods, and call open from the returned panelRef.
 *
 * @param {!Object=} config Specific configuration object that may contain
 *     the properties defined in `$mdPanel.create`.
 * @returns {!angular.$q.Promise<!MdPanelRef>} panelRef A promise that resolves
 *     to an instance of the panel.
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelPosition
 * @description
 * Returns a new instance of the MdPanelPosition object. Use this to create
 * the position config object.
 *
 * @returns {!MdPanelPosition} panelPosition
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelAnimation
 * @description
 * Returns a new instance of the MdPanelAnimation object. Use this to create
 * the animation config object.
 *
 * @returns {!MdPanelAnimation} panelAnimation
 */

/**
 * @ngdoc method
 * @name $mdPanel#newPanelGroup
 * @description
 * Creates a panel group and adds it to a tracked list of panel groups.
 *
 * @param {string} groupName Name of the group to create.
 * @param {!Object=} config Specific configuration object that may contain the
 *     following properties:
 *
 *   - `maxOpen` - `{number=}`: The maximum number of panels that are allowed to
 *     be open within a defined panel group.
 *
 * @returns {!Object<string,
 *     {panels: !Array<!MdPanelRef>,
 *     openPanels: !Array<!MdPanelRef>,
 *     maxOpen: number}>} panelGroup
 */

/**
 * @ngdoc method
 * @name $mdPanel#setGroupMaxOpen
 * @description
 * Sets the maximum number of panels in a group that can be opened at a given
 * time.
 *
 * @param {string} groupName The name of the group to configure.
 * @param {number} maxOpen The maximum number of panels that can be
 *     opened. Infinity can be passed in to remove the maxOpen limit.
 */


/*****************************************************************************
 *                                 MdPanelRef                                *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelRef
 * @module material.components.panel
 * @description
 * A reference to a created panel. This reference contains a unique id for the
 * panel, along with the following properties:
 *
 *   - `id` - `{string}`: The unique id for the panel. This id is used to track
 *     when a panel was interacted with.
 *   - `config` - `{!Object=}`: The entire config object that was used in
 *     create.
 *   - `isAttached` - `{boolean}`: Whether the panel is attached to the DOM.
 *     Visibility to the user does not factor into isAttached.
 *   - `panelContainer` - `{angular.JQLite}`: The wrapper element containing the
 *     panel. This property is added in order to have access to the `addClass`,
 *     `removeClass`, `toggleClass`, etc methods.
 *   - `panelEl` - `{angular.JQLite}`: The panel element. This property is added
 *     in order to have access to the `addClass`, `removeClass`, `toggleClass`,
 *     etc methods.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#open
 * @description
 * Attaches and shows the panel.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel is
 *     opened.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#close
 * @description
 * Hides and detaches the panel. Note that this will **not** destroy the panel.
 * If you don't intend on using the panel again, call the {@link #destroy
 * destroy} method afterwards.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel is
 *     closed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#attach
 * @description
 * Create the panel elements and attach them to the DOM. The panel will be
 * hidden by default.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel is
 *     attached.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#detach
 * @description
 * Removes the panel from the DOM. This will NOT hide the panel before removing
 * it.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel is
 *     detached.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#show
 * @description
 * Shows the panel.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel has
 *     shown and animations are completed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#hide
 * @description
 * Hides the panel.
 *
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel has
 *     hidden and animations are completed.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#destroy
 * @description
 * Destroys the panel. The panel cannot be opened again after this is called.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#addClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Adds a class to the panel. DO NOT use this hide/show the panel.
 *
 * @param {string} newClass class to be added.
 * @param {boolean} toElement Whether or not to add the class to the panel
 *     element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Removes a class from the panel. DO NOT use this to hide/show the panel.
 *
 * @param {string} oldClass Class to be removed.
 * @param {boolean} fromElement Whether or not to remove the class from the
 *     panel element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#toggleClass
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 * @description
 * Toggles a class on the panel. DO NOT use this to hide/show the panel.
 *
 * @param {string} toggleClass Class to be toggled.
 * @param {boolean} onElement Whether or not to remove the class from the panel
 *     element instead of the container.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#updatePosition
 * @description
 * Updates the position configuration of a panel. Use this to update the
 * position of a panel that is open, without having to close and re-open the
 * panel.
 *
 * @param {!MdPanelPosition} position
 */

/**
 * @ngdoc method
 * @name MdPanelRef#addToGroup
 * @description
 * Adds a panel to a group if the panel does not exist within the group already.
 * A panel can only exist within a single group.
 *
 * @param {string} groupName The name of the group to add the panel to.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeFromGroup
 * @description
 * Removes a panel from a group if the panel exists within that group. The group
 * must be created ahead of time.
 *
 * @param {string} groupName The name of the group.
 */

/**
 * @ngdoc method
 * @name MdPanelRef#registerInterceptor
 * @description
 * Registers an interceptor with the panel. The callback should return a promise,
 * which will allow the action to continue when it gets resolved, or will
 * prevent an action if it is rejected. The interceptors are called sequentially
 * and it reverse order. `type` must be one of the following
 * values available on `$mdPanel.interceptorTypes`:
 * * `CLOSE` - Gets called before the panel begins closing.
 *
 * @param {string} type Type of interceptor.
 * @param {!angular.$q.Promise<any>} callback Callback to be registered.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeInterceptor
 * @description
 * Removes a registered interceptor.
 *
 * @param {string} type Type of interceptor to be removed.
 * @param {function(): !angular.$q.Promise<any>} callback Interceptor to be removed.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#removeAllInterceptors
 * @description
 * Removes all interceptors. If a type is supplied, only the
 * interceptors of that type will be cleared.
 *
 * @param {string=} type Type of interceptors to be removed.
 * @returns {!MdPanelRef}
 */

/**
 * @ngdoc method
 * @name MdPanelRef#updateAnimation
 * @description
 * Updates the animation configuration for a panel. You can use this to change
 * the panel's animation without having to re-create it.
 *
 * @param {!MdPanelAnimation} animation
 */


/*****************************************************************************
 *                               MdPanelPosition                            *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelPosition
 * @module material.components.panel
 * @description
 *
 * Object for configuring the position of the panel.
 *
 * @usage
 *
 * #### Centering the panel
 *
 * <hljs lang="js">
 * new MdPanelPosition().absolute().center();
 * </hljs>
 *
 * #### Overlapping the panel with an element
 *
 * <hljs lang="js">
 * new MdPanelPosition()
 *     .relativeTo(someElement)
 *     .addPanelPosition(
 *       $mdPanel.xPosition.ALIGN_START,
 *       $mdPanel.yPosition.ALIGN_TOPS
 *     );
 * </hljs>
 *
 * #### Aligning the panel with the bottom of an element
 *
 * <hljs lang="js">
 * new MdPanelPosition()
 *     .relativeTo(someElement)
 *     .addPanelPosition($mdPanel.xPosition.CENTER, $mdPanel.yPosition.BELOW);
 * </hljs>
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#absolute
 * @description
 * Positions the panel absolutely relative to the parent element. If the parent
 * is document.body, this is equivalent to positioning the panel absolutely
 * within the viewport.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#relativeTo
 * @description
 * Positions the panel relative to a specific element.
 *
 * @param {string|!Element|!angular.JQLite} element Query selector, DOM element,
 *     or angular element to position the panel with respect to.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#top
 * @description
 * Sets the value of `top` for the panel. Clears any previously set vertical
 * position.
 *
 * @param {string=} top Value of `top`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#bottom
 * @description
 * Sets the value of `bottom` for the panel. Clears any previously set vertical
 * position.
 *
 * @param {string=} bottom Value of `bottom`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#start
 * @description
 * Sets the panel to the start of the page - `left` if `ltr` or `right` for
 * `rtl`. Clears any previously set horizontal position.
 *
 * @param {string=} start Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#end
 * @description
 * Sets the panel to the end of the page - `right` if `ltr` or `left` for `rtl`.
 * Clears any previously set horizontal position.
 *
 * @param {string=} end Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#left
 * @description
 * Sets the value of `left` for the panel. Clears any previously set
 * horizontal position.
 *
 * @param {string=} left Value of `left`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#right
 * @description
 * Sets the value of `right` for the panel. Clears any previously set
 * horizontal position.
 *
 * @param {string=} right Value of `right`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#centerHorizontally
 * @description
 * Centers the panel horizontally in the viewport. Clears any previously set
 * horizontal position.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#centerVertically
 * @description
 * Centers the panel vertically in the viewport. Clears any previously set
 * vertical position.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#center
 * @description
 * Centers the panel horizontally and vertically in the viewport. This is
 * equivalent to calling both `centerHorizontally` and `centerVertically`.
 * Clears any previously set horizontal and vertical positions.
 *
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#addPanelPosition
 * @description
 * Sets the x and y position for the panel relative to another element. Can be
 * called multiple times to specify an ordered list of panel positions. The
 * first position which allows the panel to be completely on-screen will be
 * chosen; the last position will be chose whether it is on-screen or not.
 *
 * xPosition must be one of the following values available on
 * $mdPanel.xPosition:
 *
 *
 * CENTER | ALIGN_START | ALIGN_END | OFFSET_START | OFFSET_END
 *
 * <pre>
 *    *************
 *    *           *
 *    *   PANEL   *
 *    *           *
 *    *************
 *   A B    C    D E
 *
 * A: OFFSET_START (for LTR displays)
 * B: ALIGN_START (for LTR displays)
 * C: CENTER
 * D: ALIGN_END (for LTR displays)
 * E: OFFSET_END (for LTR displays)
 * </pre>
 *
 * yPosition must be one of the following values available on
 * $mdPanel.yPosition:
 *
 * CENTER | ALIGN_TOPS | ALIGN_BOTTOMS | ABOVE | BELOW
 *
 * <pre>
 *   F
 *   G *************
 *     *           *
 *   H *   PANEL   *
 *     *           *
 *   I *************
 *   J
 *
 * F: BELOW
 * G: ALIGN_TOPS
 * H: CENTER
 * I: ALIGN_BOTTOMS
 * J: ABOVE
 * </pre>
 *
 * @param {string} xPosition
 * @param {string} yPosition
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#withOffsetX
 * @description
 * Sets the value of the offset in the x-direction.
 *
 * @param {string} offsetX
 * @returns {!MdPanelPosition}
 */

/**
 * @ngdoc method
 * @name MdPanelPosition#withOffsetY
 * @description
 * Sets the value of the offset in the y-direction.
 *
 * @param {string} offsetY
 * @returns {!MdPanelPosition}
 */


/*****************************************************************************
 *                               MdPanelAnimation                            *
 *****************************************************************************/


/**
 * @ngdoc type
 * @name MdPanelAnimation
 * @module material.components.panel
 * @description
 * Animation configuration object. To use, create an MdPanelAnimation with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * @usage
 *
 * <hljs lang="js">
 * var panelAnimation = new MdPanelAnimation()
 *     .openFrom(myButtonEl)
 *     .duration(1337)
 *     .closeTo('.my-button')
 *     .withAnimation($mdPanel.animation.SCALE);
 *
 * $mdPanel.create({
 *   animation: panelAnimation
 * });
 * </hljs>
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#openFrom
 * @description
 * Specifies where to start the open animation. `openFrom` accepts a
 * click event object, query selector, DOM element, or a Rect object that
 * is used to determine the bounds. When passed a click event, the location
 * of the click will be used as the position to start the animation.
 *
 * @param {string|!Element|!Event|{top: number, left: number}}
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#closeTo
 * @description
 * Specifies where to animate the panel close. `closeTo` accepts a
 * query selector, DOM element, or a Rect object that is used to determine
 * the bounds.
 *
 * @param {string|!Element|{top: number, left: number}}
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#withAnimation
 * @description
 * Specifies the animation class.
 *
 * There are several default animations that can be used:
 * ($mdPanel.animation)
 *   SLIDE: The panel slides in and out from the specified
 *       elements. It will not fade in or out.
 *   SCALE: The panel scales in and out. Slide and fade are
 *       included in this animation.
 *   FADE: The panel fades in and out.
 *
 * Custom classes will by default fade in and out unless
 * "transition: opacity 1ms" is added to the to custom class.
 *
 * @param {string|{open: string, close: string}} cssClass
 * @returns {!MdPanelAnimation}
 */

/**
 * @ngdoc method
 * @name MdPanelAnimation#duration
 * @description
 * Specifies the duration of the animation in milliseconds. The `duration`
 * method accepts either a number or an object with separate open and close
 * durations.
 *
 * @param {number|{open: number, close: number}} duration
 * @returns {!MdPanelAnimation}
 */


/*****************************************************************************
 *                            PUBLIC DOCUMENTATION                           *
 *****************************************************************************/


var MD_PANEL_Z_INDEX = 80;
var MD_PANEL_HIDDEN = '_md-panel-hidden';
var FOCUS_TRAP_TEMPLATE = angular.element(
    '<div class="_md-panel-focus-trap" tabindex="0"></div>');

var _presets = {};


/**
 * A provider that is used for creating presets for the panel API.
 * @final @constructor @ngInject
 */
function MdPanelProvider() {
  return {
    'definePreset': definePreset,
    'getAllPresets': getAllPresets,
    'clearPresets': clearPresets,
    '$get': $getProvider()
  };
}


/**
 * Takes the passed in panel configuration object and adds it to the `_presets`
 * object at the specified name.
 * @param {string} name Name of the preset to set.
 * @param {!Object} preset Specific configuration object that can contain any
 *     and all of the parameters avaialble within the `$mdPanel.create` method.
 *     However, parameters that pertain to id, position, animation, and user
 *     interaction are not allowed and will be removed from the preset
 *     configuration.
 */
function definePreset(name, preset) {
  if (!name || !preset) {
    throw new Error('mdPanelProvider: The panel preset definition is ' +
        'malformed. The name and preset object are required.');
  } else if (_presets.hasOwnProperty(name)) {
    throw new Error('mdPanelProvider: The panel preset you have requested ' +
        'has already been defined.');
  }

  // Delete any property on the preset that is not allowed.
  delete preset.id;
  delete preset.position;
  delete preset.animation;

  _presets[name] = preset;
}


/**
 * Gets a clone of the `_presets`.
 * @return {!Object}
 */
function getAllPresets() {
  return angular.copy(_presets);
}


/**
 * Clears all of the stored presets.
 */
function clearPresets() {
  _presets = {};
}


/**
 * Represents the `$get` method of the AngularJS provider. From here, a new
 * reference to the MdPanelService is returned where the needed arguments are
 * passed in including the MdPanelProvider `_presets`.
 * @param {!Object} _presets
 * @param {!angular.JQLite} $rootElement
 * @param {!angular.Scope} $rootScope
 * @param {!angular.$injector} $injector
 * @param {!angular.$window} $window
 */
function $getProvider() {
  return [
    '$rootElement', '$rootScope', '$injector', '$window',
    function($rootElement, $rootScope, $injector, $window) {
      return new MdPanelService(_presets, $rootElement, $rootScope,
          $injector, $window);
    }
  ];
}


/*****************************************************************************
 *                               MdPanel Service                             *
 *****************************************************************************/


/**
 * A service that is used for controlling/displaying panels on the screen.
 * @param {!Object} presets
 * @param {!angular.JQLite} $rootElement
 * @param {!angular.Scope} $rootScope
 * @param {!angular.$injector} $injector
 * @param {!angular.$window} $window
 * @final @constructor @ngInject
 */
function MdPanelService(presets, $rootElement, $rootScope, $injector, $window) {
  /**
   * Default config options for the panel.
   * Anything angular related needs to be done later. Therefore
   *     scope: $rootScope.$new(true),
   *     attachTo: $rootElement,
   * are added later.
   * @private {!Object}
   */
  this._defaultConfigOptions = {
    bindToController: true,
    clickOutsideToClose: false,
    disableParentScroll: false,
    escapeToClose: false,
    focusOnOpen: true,
    fullscreen: false,
    hasBackdrop: false,
    propagateContainerEvents: false,
    transformTemplate: angular.bind(this, this._wrapTemplate),
    trapFocus: false,
    zIndex: MD_PANEL_Z_INDEX
  };

  /** @private {!Object} */
  this._config = {};

  /** @private {!Object} */
  this._presets = presets;

  /** @private @const */
  this._$rootElement = $rootElement;

  /** @private @const */
  this._$rootScope = $rootScope;

  /** @private @const */
  this._$injector = $injector;

  /** @private @const */
  this._$window = $window;

  /** @private @const */
  this._$mdUtil = this._$injector.get('$mdUtil');

  /** @private {!Object<string, !MdPanelRef>} */
  this._trackedPanels = {};

  /**
   * @private {!Object<string,
   *     {panels: !Array<!MdPanelRef>,
   *     openPanels: !Array<!MdPanelRef>,
   *     maxOpen: number}>}
   */
  this._groups = Object.create(null);

  /**
   * Default animations that can be used within the panel.
   * @type {enum}
   */
  this.animation = MdPanelAnimation.animation;

  /**
   * Possible values of xPosition for positioning the panel relative to
   * another element.
   * @type {enum}
   */
  this.xPosition = MdPanelPosition.xPosition;

  /**
   * Possible values of yPosition for positioning the panel relative to
   * another element.
   * @type {enum}
   */
  this.yPosition = MdPanelPosition.yPosition;

  /**
   * Possible values for the interceptors that can be registered on a panel.
   * @type {enum}
   */
  this.interceptorTypes = MdPanelRef.interceptorTypes;

  /**
   * Possible values for closing of a panel.
   * @type {enum}
   */
  this.closeReasons = MdPanelRef.closeReasons;

  /**
   * Possible values of absolute position.
   * @type {enum}
   */
  this.absPosition = MdPanelPosition.absPosition;
}


/**
 * Creates a panel with the specified options.
 * @param {string=} preset Name of a preset configuration that can be used to
 *     extend the panel configuration.
 * @param {!Object=} config Configuration object for the panel.
 * @returns {!MdPanelRef}
 */
MdPanelService.prototype.create = function(preset, config) {
  if (typeof preset === 'string') {
    preset = this._getPresetByName(preset);
  } else if (typeof preset === 'object' &&
      (angular.isUndefined(config) || !config)) {
    config = preset;
    preset = {};
  }

  preset = preset || {};
  config = config || {};

  // If the passed-in config contains an ID and the ID is within _trackedPanels,
  // return the tracked panel after updating its config with the passed-in
  // config.
  if (angular.isDefined(config.id) && this._trackedPanels[config.id]) {
    var trackedPanel = this._trackedPanels[config.id];
    angular.extend(trackedPanel.config, config);
    return trackedPanel;
  }

  // Combine the passed-in config, the _defaultConfigOptions, and the preset
  // configuration into the `_config`.
  this._config = angular.extend({
    // If no ID is set within the passed-in config, then create an arbitrary ID.
    id: config.id || 'panel_' + this._$mdUtil.nextUid(),
    scope: this._$rootScope.$new(true),
    attachTo: this._$rootElement
  }, this._defaultConfigOptions, config, preset);

  // Create the panelRef and add it to the `_trackedPanels` object.
  var panelRef = new MdPanelRef(this._config, this._$injector);
  this._trackedPanels[config.id] = panelRef;

  // Add the panel to each of its requested groups.
  if (this._config.groupName) {
    if (angular.isString(this._config.groupName)) {
      this._config.groupName = [this._config.groupName];
    }
    angular.forEach(this._config.groupName, function(group) {
      panelRef.addToGroup(group);
    });
  }

  this._config.scope.$on('$destroy', angular.bind(panelRef, panelRef.detach));

  return panelRef;
};


/**
 * Creates and opens a panel with the specified options.
 * @param {string=} preset Name of a preset configuration that can be used to
 *     extend the panel configuration.
 * @param {!Object=} config Configuration object for the panel.
 * @returns {!angular.$q.Promise<!MdPanelRef>} The panel created from create.
 */
MdPanelService.prototype.open = function(preset, config) {
  var panelRef = this.create(preset, config);
  return panelRef.open().then(function() {
    return panelRef;
  });
};


/**
 * Gets a specific preset configuration object saved within `_presets`.
 * @param {string} preset Name of the preset to search for.
 * @returns {!Object} The preset configuration object.
 */
MdPanelService.prototype._getPresetByName = function(preset) {
  if (!this._presets[preset]) {
    throw new Error('mdPanel: The panel preset configuration that you ' +
        'requested does not exist. Use the $mdPanelProvider to create a ' +
        'preset before requesting one.');
  }
  return this._presets[preset];
};


/**
 * Returns a new instance of the MdPanelPosition. Use this to create the
 * positioning object.
 * @returns {!MdPanelPosition}
 */
MdPanelService.prototype.newPanelPosition = function() {
  return new MdPanelPosition(this._$injector);
};


/**
 * Returns a new instance of the MdPanelAnimation. Use this to create the
 * animation object.
 * @returns {!MdPanelAnimation}
 */
MdPanelService.prototype.newPanelAnimation = function() {
  return new MdPanelAnimation(this._$injector);
};


/**
 * Creates a panel group and adds it to a tracked list of panel groups.
 * @param groupName {string} Name of the group to create.
 * @param config {!Object=} Specific configuration object that may contain the
 *     following properties:
 *
 *   - `maxOpen` - `{number=}`: The maximum number of panels that are allowed
 *     open within a defined panel group.
 *
 * @returns {!Object<string,
 *     {panels: !Array<!MdPanelRef>,
 *     openPanels: !Array<!MdPanelRef>,
 *     maxOpen: number}>} panelGroup
 */
MdPanelService.prototype.newPanelGroup = function(groupName, config) {
  if (!this._groups[groupName]) {
    config = config || {};
    var group = {
      panels: [],
      openPanels: [],
      maxOpen: config.maxOpen > 0 ? config.maxOpen : Infinity
    };
    this._groups[groupName] = group;
  }
  return this._groups[groupName];
};


/**
 * Sets the maximum number of panels in a group that can be opened at a given
 * time.
 * @param {string} groupName The name of the group to configure.
 * @param {number} maxOpen The maximum number of panels that can be
 *     opened. Infinity can be passed in to remove the maxOpen limit.
 */
MdPanelService.prototype.setGroupMaxOpen = function(groupName, maxOpen) {
  if (this._groups[groupName]) {
    this._groups[groupName].maxOpen = maxOpen;
  } else {
    throw new Error('mdPanel: Group does not exist yet. Call newPanelGroup().');
  }
};


/**
 * Determines if the current number of open panels within a group exceeds the
 * limit of allowed open panels.
 * @param {string} groupName The name of the group to check.
 * @returns {boolean} true if open count does exceed maxOpen and false if not.
 * @private
 */
MdPanelService.prototype._openCountExceedsMaxOpen = function(groupName) {
  if (this._groups[groupName]) {
    var group = this._groups[groupName];
    return group.maxOpen > 0 && group.openPanels.length > group.maxOpen;
  }
  return false;
};


/**
 * Closes the first open panel within a specific group.
 * @param {string} groupName The name of the group.
 * @private
 */
MdPanelService.prototype._closeFirstOpenedPanel = function(groupName) {
  this._groups[groupName].openPanels[0].close();
};


/**
 * Wraps the users template in two elements, md-panel-outer-wrapper, which
 * covers the entire attachTo element, and md-panel, which contains only the
 * template. This allows the panel control over positioning, animations,
 * and similar properties.
 * @param {string} origTemplate The original template.
 * @returns {string} The wrapped template.
 * @private
 */
MdPanelService.prototype._wrapTemplate = function(origTemplate) {
  var template = origTemplate || '';

  // The panel should be initially rendered offscreen so we can calculate
  // height and width for positioning.
  return '' +
      '<div class="md-panel-outer-wrapper">' +
      '  <div class="md-panel _md-panel-offscreen">' + template + '</div>' +
      '</div>';
};


/**
 * Wraps a content element in a md-panel-outer wrapper and
 * positions it off-screen. Allows for proper control over positoning
 * and animations.
 * @param {!angular.JQLite} contentElement Element to be wrapped.
 * @return {!angular.JQLite} Wrapper element.
 * @private
 */
MdPanelService.prototype._wrapContentElement = function(contentElement) {
  var wrapper = angular.element('<div class="md-panel-outer-wrapper">');

  contentElement.addClass('md-panel _md-panel-offscreen');
  wrapper.append(contentElement);

  return wrapper;
};


/*****************************************************************************
 *                                 MdPanelRef                                *
 *****************************************************************************/


/**
 * A reference to a created panel. This reference contains a unique id for the
 * panel, along with properties/functions used to control the panel.
 * @param {!Object} config
 * @param {!angular.$injector} $injector
 * @final @constructor
 */
function MdPanelRef(config, $injector) {
  // Injected variables.
  /** @private @const {!angular.$q} */
  this._$q = $injector.get('$q');

  /** @private @const {!angular.$mdCompiler} */
  this._$mdCompiler = $injector.get('$mdCompiler');

  /** @private @const {!angular.$mdConstant} */
  this._$mdConstant = $injector.get('$mdConstant');

  /** @private @const {!angular.$mdUtil} */
  this._$mdUtil = $injector.get('$mdUtil');

  /** @private @const {!angular.$mdTheming} */
  this._$mdTheming = $injector.get('$mdTheming');

  /** @private @const {!angular.Scope} */
  this._$rootScope = $injector.get('$rootScope');

  /** @private @const {!angular.$animate} */
  this._$animate = $injector.get('$animate');

  /** @private @const {!MdPanelRef} */
  this._$mdPanel = $injector.get('$mdPanel');

  /** @private @const {!angular.$log} */
  this._$log = $injector.get('$log');

  /** @private @const {!angular.$window} */
  this._$window = $injector.get('$window');

  /** @private @const {!Function} */
  this._$$rAF = $injector.get('$$rAF');

  // Public variables.
  /**
   * Unique id for the panelRef.
   * @type {string}
   */
  this.id = config.id;

  /** @type {!Object} */
  this.config = config;

  /** @type {!angular.JQLite|undefined} */
  this.panelContainer;

  /** @type {!angular.JQLite|undefined} */
  this.panelEl;

  /**
   * Whether the panel is attached. This is synchronous. When attach is called,
   * isAttached is set to true. When detach is called, isAttached is set to
   * false.
   * @type {boolean}
   */
  this.isAttached = false;

  // Private variables.
  /** @private {Array<function()>} */
  this._removeListeners = [];

  /** @private {!angular.JQLite|undefined} */
  this._topFocusTrap;

  /** @private {!angular.JQLite|undefined} */
  this._bottomFocusTrap;

  /** @private {!$mdPanel|undefined} */
  this._backdropRef;

  /** @private {Function?} */
  this._restoreScroll = null;

  /**
   * Keeps track of all the panel interceptors.
   * @private {!Object}
   */
  this._interceptors = Object.create(null);

  /**
   * Cleanup function, provided by `$mdCompiler` and assigned after the element
   * has been compiled. When `contentElement` is used, the function is used to
   * restore the element to it's proper place in the DOM.
   * @private {!Function}
   */
  this._compilerCleanup = null;

  /**
   * Cache for saving and restoring element inline styles, CSS classes etc.
   * @type {{styles: string, classes: string}}
   */
  this._restoreCache = {
    styles: '',
    classes: ''
  };
}


MdPanelRef.interceptorTypes = {
  CLOSE: 'onClose'
};


/**
 * Opens an already created and configured panel. If the panel is already
 * visible, does nothing.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel is opened and animations finish.
 */
MdPanelRef.prototype.open = function() {
  var self = this;
  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var show = self._simpleBind(self.show, self);
    var checkGroupMaxOpen = function() {
      if (self.config.groupName) {
        angular.forEach(self.config.groupName, function(group) {
          if (self._$mdPanel._openCountExceedsMaxOpen(group)) {
            self._$mdPanel._closeFirstOpenedPanel(group);
          }
        });
      }
    };

    self.attach()
        .then(show)
        .then(checkGroupMaxOpen)
        .then(done)
        .catch(reject);
  });
};


/**
 * Closes the panel.
 * @param {string} closeReason The event type that triggered the close.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel is closed and animations finish.
 */
MdPanelRef.prototype.close = function(closeReason) {
  var self = this;

  return this._$q(function(resolve, reject) {
    self._callInterceptors(MdPanelRef.interceptorTypes.CLOSE).then(function() {
      var done = self._done(resolve, self);
      var detach = self._simpleBind(self.detach, self);
      var onCloseSuccess = self.config['onCloseSuccess'] || angular.noop;
      onCloseSuccess = angular.bind(self, onCloseSuccess, self, closeReason);

      self.hide()
          .then(detach)
          .then(done)
          .then(onCloseSuccess)
          .catch(reject);
    }, reject);
  });
};


/**
 * Attaches the panel. The panel will be hidden afterwards.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel is attached.
 */
MdPanelRef.prototype.attach = function() {
  if (this.isAttached && this.panelEl) {
    return this._$q.when(this);
  }

  var self = this;
  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onDomAdded = self.config['onDomAdded'] || angular.noop;
    var addListeners = function(response) {
      self.isAttached = true;
      self._addEventListeners();
      return response;
    };

    self._$q.all([
        self._createBackdrop(),
        self._createPanel()
            .then(addListeners)
            .catch(reject)
    ]).then(onDomAdded)
      .then(done)
      .catch(reject);
  });
};


/**
 * Only detaches the panel. Will NOT hide the panel first.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel is detached.
 */
MdPanelRef.prototype.detach = function() {
  if (!this.isAttached) {
    return this._$q.when(this);
  }

  var self = this;
  var onDomRemoved = self.config['onDomRemoved'] || angular.noop;

  var detachFn = function() {
    self._removeEventListeners();

    // Remove the focus traps that we added earlier for keeping focus within
    // the panel.
    if (self._topFocusTrap && self._topFocusTrap.parentNode) {
      self._topFocusTrap.parentNode.removeChild(self._topFocusTrap);
    }

    if (self._bottomFocusTrap && self._bottomFocusTrap.parentNode) {
      self._bottomFocusTrap.parentNode.removeChild(self._bottomFocusTrap);
    }

    if (self._restoreCache.classes) {
      self.panelEl[0].className = self._restoreCache.classes;
    }

    // Either restore the saved styles or clear the ones set by mdPanel.
    self.panelEl[0].style.cssText = self._restoreCache.styles || '';

    self._compilerCleanup();
    self.panelContainer.remove();
    self.isAttached = false;
    return self._$q.when(self);
  };

  if (this._restoreScroll) {
    this._restoreScroll();
    this._restoreScroll = null;
  }

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);

    self._$q.all([
      detachFn(),
      self._backdropRef ? self._backdropRef.detach() : true
    ]).then(onDomRemoved)
      .then(done)
      .catch(reject);
  });
};


/**
 * Destroys the panel. The Panel cannot be opened again after this.
 */
MdPanelRef.prototype.destroy = function() {
  var self = this;
  if (this.config.groupName) {
    angular.forEach(this.config.groupName, function(group) {
      self.removeFromGroup(group);
    });
  }
  this.config.scope.$destroy();
  this.config.locals = null;
  this._interceptors = null;
};


/**
 * Shows the panel.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel has shown and animations finish.
 */
MdPanelRef.prototype.show = function() {
  if (!this.panelContainer) {
    return this._$q(function(resolve, reject) {
      reject('mdPanel: Panel does not exist yet. Call open() or attach().');
    });
  }

  if (!this.panelContainer.hasClass(MD_PANEL_HIDDEN)) {
    return this._$q.when(this);
  }

  var self = this;
  var animatePromise = function() {
    self.panelContainer.removeClass(MD_PANEL_HIDDEN);
    return self._animateOpen();
  };

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onOpenComplete = self.config['onOpenComplete'] || angular.noop;
    var addToGroupOpen = function() {
      if (self.config.groupName) {
        angular.forEach(self.config.groupName, function(group) {
          self._$mdPanel._groups[group].openPanels.push(self);
        });
      }
    };

    self._$q.all([
      self._backdropRef ? self._backdropRef.show() : self,
      animatePromise().then(function() { self._focusOnOpen(); }, reject)
    ]).then(onOpenComplete)
      .then(addToGroupOpen)
      .then(done)
      .catch(reject);
  });
};


/**
 * Hides the panel.
 * @returns {!angular.$q.Promise<!MdPanelRef>} A promise that is resolved when
 *     the panel has hidden and animations finish.
 */
MdPanelRef.prototype.hide = function() {
  if (!this.panelContainer) {
    return this._$q(function(resolve, reject) {
      reject('mdPanel: Panel does not exist yet. Call open() or attach().');
    });
  }

  if (this.panelContainer.hasClass(MD_PANEL_HIDDEN)) {
    return this._$q.when(this);
  }

  var self = this;

  return this._$q(function(resolve, reject) {
    var done = self._done(resolve, self);
    var onRemoving = self.config['onRemoving'] || angular.noop;
    var hidePanel = function() {
      self.panelContainer.addClass(MD_PANEL_HIDDEN);
    };
    var removeFromGroupOpen = function() {
      if (self.config.groupName) {
        var group, index;
        angular.forEach(self.config.groupName, function(group) {
          group = self._$mdPanel._groups[group];
          index = group.openPanels.indexOf(self);
          if (index > -1) {
            group.openPanels.splice(index, 1);
          }
        });
      }
    };
    var focusOnOrigin = function() {
      var origin = self.config['origin'];
      if (origin) {
        getElement(origin).focus();
      }
    };

    self._$q.all([
      self._backdropRef ? self._backdropRef.hide() : self,
      self._animateClose()
          .then(onRemoving)
          .then(hidePanel)
          .then(removeFromGroupOpen)
          .then(focusOnOrigin)
          .catch(reject)
    ]).then(done, reject);
  });
};


/**
 * Add a class to the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} newClass Class to be added.
 * @param {boolean} toElement Whether or not to add the class to the panel
 *     element instead of the container.
 */
MdPanelRef.prototype.addClass = function(newClass, toElement) {
  this._$log.warn(
      'mdPanel: The addClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!toElement && !this.panelContainer.hasClass(newClass)) {
    this.panelContainer.addClass(newClass);
  } else if (toElement && !this.panelEl.hasClass(newClass)) {
    this.panelEl.addClass(newClass);
  }
};


/**
 * Remove a class from the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} oldClass Class to be removed.
 * @param {boolean} fromElement Whether or not to remove the class from the
 *     panel element instead of the container.
 */
MdPanelRef.prototype.removeClass = function(oldClass, fromElement) {
  this._$log.warn(
      'mdPanel: The removeClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!fromElement && this.panelContainer.hasClass(oldClass)) {
    this.panelContainer.removeClass(oldClass);
  } else if (fromElement && this.panelEl.hasClass(oldClass)) {
    this.panelEl.removeClass(oldClass);
  }
};


/**
 * Toggle a class on the panel. DO NOT use this to hide/show the panel.
 * @deprecated
 * This method is in the process of being deprecated in favor of using the panel
 * and container JQLite elements that are referenced in the MdPanelRef object.
 * Full deprecation is scheduled for material 1.2.
 *
 * @param {string} toggleClass The class to toggle.
 * @param {boolean} onElement Whether or not to toggle the class on the panel
 *     element instead of the container.
 */
MdPanelRef.prototype.toggleClass = function(toggleClass, onElement) {
  this._$log.warn(
      'mdPanel: The toggleClass method is in the process of being deprecated. ' +
      'Full deprecation is scheduled for the AngularJS Material 1.2 release. ' +
      'To achieve the same results, use the panelContainer or panelEl ' +
      'JQLite elements that are referenced in MdPanelRef.');

  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  if (!onElement) {
    this.panelContainer.toggleClass(toggleClass);
  } else {
    this.panelEl.toggleClass(toggleClass);
  }
};


/**
 * Compiles the panel, according to the passed in config and appends it to
 * the DOM. Helps normalize differences in the compilation process between
 * using a string template and a content element.
 * @returns {!angular.$q.Promise<!MdPanelRef>} Promise that is resolved when
 *     the element has been compiled and added to the DOM.
 * @private
 */
MdPanelRef.prototype._compile = function() {
  var self = this;

  // Compile the element via $mdCompiler. Note that when using a
  // contentElement, the element isn't actually being compiled, rather the
  // compiler saves it's place in the DOM and provides a way of restoring it.
  return self._$mdCompiler.compile(self.config).then(function(compileData) {
    var config = self.config;

    if (config.contentElement) {
      var panelEl = compileData.element;

      // Since mdPanel modifies the inline styles and CSS classes, we need
      // to save them in order to be able to restore on close.
      self._restoreCache.styles = panelEl[0].style.cssText;
      self._restoreCache.classes = panelEl[0].className;

      self.panelContainer = self._$mdPanel._wrapContentElement(panelEl);
      self.panelEl = panelEl;
    } else {
      self.panelContainer = compileData.link(config['scope']);
      self.panelEl = angular.element(
        self.panelContainer[0].querySelector('.md-panel')
      );
    }

    // Save a reference to the cleanup function from the compiler.
    self._compilerCleanup = compileData.cleanup;

    // Attach the panel to the proper place in the DOM.
    getElement(self.config['attachTo']).append(self.panelContainer);

    return self;
  });
};


/**
 * Creates a panel and adds it to the dom.
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel is
 *     created.
 * @private
 */
MdPanelRef.prototype._createPanel = function() {
  var self = this;

  return this._$q(function(resolve, reject) {
    if (!self.config.locals) {
      self.config.locals = {};
    }

    self.config.locals.mdPanelRef = self;

    self._compile().then(function() {
      if (self.config['disableParentScroll']) {
        self._restoreScroll = self._$mdUtil.disableScrollAround(
          null,
          self.panelContainer,
          { disableScrollMask: true }
        );
      }

      // Add a custom CSS class to the panel element.
      if (self.config['panelClass']) {
        self.panelEl.addClass(self.config['panelClass']);
      }

      // Handle click and touch events for the panel container.
      if (self.config['propagateContainerEvents']) {
        self.panelContainer.css('pointer-events', 'none');
        self.panelEl.css('pointer-events', 'all');
      }

      // Panel may be outside the $rootElement, tell ngAnimate to animate
      // regardless.
      if (self._$animate.pin) {
        self._$animate.pin(
          self.panelContainer,
          getElement(self.config['attachTo'])
        );
      }

      self._configureTrapFocus();
      self._addStyles().then(function() {
        resolve(self);
      }, reject);
    }, reject);

  });
};


/**
 * Adds the styles for the panel, such as positioning and z-index. Also,
 * themes the panel element and panel container using `$mdTheming`.
 * @returns {!angular.$q.Promise<!MdPanelRef>}
 * @private
 */
MdPanelRef.prototype._addStyles = function() {
  var self = this;
  return this._$q(function(resolve) {
    self.panelContainer.css('z-index', self.config['zIndex']);
    self.panelEl.css('z-index', self.config['zIndex'] + 1);

    var hideAndResolve = function() {
      // Theme the element and container.
      self._setTheming();

      // Remove offscreen class and add hidden class.
      self.panelEl.removeClass('_md-panel-offscreen');
      self.panelContainer.addClass(MD_PANEL_HIDDEN);

      resolve(self);
    };

    if (self.config['fullscreen']) {
      self.panelEl.addClass('_md-panel-fullscreen');
      hideAndResolve();
      return; // Don't setup positioning.
    }

    var positionConfig = self.config['position'];
    if (!positionConfig) {
      hideAndResolve();
      return; // Don't setup positioning.
    }

    // Wait for angular to finish processing the template
    self._$rootScope['$$postDigest'](function() {
      // Position it correctly. This is necessary so that the panel will have a
      // defined height and width.
      self._updatePosition(true);

      // Theme the element and container.
      self._setTheming();

      resolve(self);
    });
  });
};


/**
 * Sets the `$mdTheming` classes on the `panelContainer` and `panelEl`.
 * @private
 */
MdPanelRef.prototype._setTheming = function() {
  this._$mdTheming(this.panelEl);
  this._$mdTheming(this.panelContainer);
};


/**
 * Updates the position configuration of a panel
 * @param {!MdPanelPosition} position
 */
MdPanelRef.prototype.updatePosition = function(position) {
  if (!this.panelContainer) {
    throw new Error(
        'mdPanel: Panel does not exist yet. Call open() or attach().');
  }

  this.config['position'] = position;
  this._updatePosition();
};


/**
 * Calculates and updates the position of the panel.
 * @param {boolean=} init
 * @private
 */
MdPanelRef.prototype._updatePosition = function(init) {
  var positionConfig = this.config['position'];

  if (positionConfig) {
    positionConfig._setPanelPosition(this.panelEl);

    // Hide the panel now that position is known.
    if (init) {
      this.panelEl.removeClass('_md-panel-offscreen');
      this.panelContainer.addClass(MD_PANEL_HIDDEN);
    }

    this.panelEl.css(
      MdPanelPosition.absPosition.TOP,
      positionConfig.getTop()
    );
    this.panelEl.css(
      MdPanelPosition.absPosition.BOTTOM,
      positionConfig.getBottom()
    );
    this.panelEl.css(
      MdPanelPosition.absPosition.LEFT,
      positionConfig.getLeft()
    );
    this.panelEl.css(
      MdPanelPosition.absPosition.RIGHT,
      positionConfig.getRight()
    );
  }
};


/**
 * Focuses on the panel or the first focus target.
 * @private
 */
MdPanelRef.prototype._focusOnOpen = function() {
  if (this.config['focusOnOpen']) {
    // Wait for the template to finish rendering to guarantee md-autofocus has
    // finished adding the class md-autofocus, otherwise the focusable element
    // isn't available to focus.
    var self = this;
    this._$rootScope['$$postDigest'](function() {
      var target = self._$mdUtil.findFocusTarget(self.panelEl) ||
          self.panelEl;
      target.focus();
    });
  }
};


/**
 * Shows the backdrop.
 * @returns {!angular.$q.Promise} A promise that is resolved when the backdrop
 *     is created and attached.
 * @private
 */
MdPanelRef.prototype._createBackdrop = function() {
  if (this.config.hasBackdrop) {
    if (!this._backdropRef) {
      var backdropAnimation = this._$mdPanel.newPanelAnimation()
          .openFrom(this.config.attachTo)
          .withAnimation({
            open: '_md-opaque-enter',
            close: '_md-opaque-leave'
          });

      if (this.config.animation) {
        backdropAnimation.duration(this.config.animation._rawDuration);
      }

      var backdropConfig = {
        animation: backdropAnimation,
        attachTo: this.config.attachTo,
        focusOnOpen: false,
        panelClass: '_md-panel-backdrop',
        zIndex: this.config.zIndex - 1
      };

      this._backdropRef = this._$mdPanel.create(backdropConfig);
    }
    if (!this._backdropRef.isAttached) {
      return this._backdropRef.attach();
    }
  }
};


/**
 * Listen for escape keys and outside clicks to auto close.
 * @private
 */
MdPanelRef.prototype._addEventListeners = function() {
  this._configureEscapeToClose();
  this._configureClickOutsideToClose();
  this._configureScrollListener();
};


/**
 * Remove event listeners added in _addEventListeners.
 * @private
 */
MdPanelRef.prototype._removeEventListeners = function() {
  this._removeListeners && this._removeListeners.forEach(function(removeFn) {
    removeFn();
  });
  this._removeListeners = [];
};


/**
 * Setup the escapeToClose event listeners.
 * @private
 */
MdPanelRef.prototype._configureEscapeToClose = function() {
  if (this.config['escapeToClose']) {
    var parentTarget = getElement(this.config['attachTo']);
    var self = this;

    var keyHandlerFn = function(ev) {
      if (ev.keyCode === self._$mdConstant.KEY_CODE.ESCAPE) {
        ev.stopPropagation();
        ev.preventDefault();

        self.close(MdPanelRef.closeReasons.ESCAPE);
      }
    };

    // Add keydown listeners
    this.panelContainer.on('keydown', keyHandlerFn);
    parentTarget.on('keydown', keyHandlerFn);

    // Queue remove listeners function
    this._removeListeners.push(function() {
      self.panelContainer.off('keydown', keyHandlerFn);
      parentTarget.off('keydown', keyHandlerFn);
    });
  }
};


/**
 * Setup the clickOutsideToClose event listeners.
 * @private
 */
MdPanelRef.prototype._configureClickOutsideToClose = function() {
  if (this.config['clickOutsideToClose']) {
    var target = this.config['propagateContainerEvents'] ?
        angular.element(document.body) :
        this.panelContainer;
    var sourceEl;

    // Keep track of the element on which the mouse originally went down
    // so that we can only close the backdrop when the 'click' started on it.
    // A simple 'click' handler does not work, it sets the target object as the
    // element the mouse went down on.
    var mousedownHandler = function(ev) {
      sourceEl = ev.target;
    };

    // We check if our original element and the target is the backdrop
    // because if the original was the backdrop and the target was inside the
    // panel we don't want to panel to close.
    var self = this;
    var mouseupHandler = function(ev) {
      if (self.config['propagateContainerEvents']) {

        // We check if the sourceEl of the event is the panel element or one
        // of it's children. If it is not, then close the panel.
        if (sourceEl !== self.panelEl[0] && !self.panelEl[0].contains(sourceEl)) {
          self.close();
        }

      } else if (sourceEl === target[0] && ev.target === target[0]) {
        ev.stopPropagation();
        ev.preventDefault();

        self.close(MdPanelRef.closeReasons.CLICK_OUTSIDE);
      }
    };

    // Add listeners
    target.on('mousedown', mousedownHandler);
    target.on('mouseup', mouseupHandler);

    // Queue remove listeners function
    this._removeListeners.push(function() {
      target.off('mousedown', mousedownHandler);
      target.off('mouseup', mouseupHandler);
    });
  }
};


/**
 * Configures the listeners for updating the panel position on scroll.
 * @private
*/
MdPanelRef.prototype._configureScrollListener = function() {
  // No need to bind the event if scrolling is disabled.
  if (!this.config['disableParentScroll']) {
    var updatePosition = angular.bind(this, this._updatePosition);
    var debouncedUpdatePosition = this._$$rAF.throttle(updatePosition);
    var self = this;

    var onScroll = function() {
      debouncedUpdatePosition();
    };

    // Add listeners.
    this._$window.addEventListener('scroll', onScroll, true);

    // Queue remove listeners function.
    this._removeListeners.push(function() {
      self._$window.removeEventListener('scroll', onScroll, true);
    });
  }
};


/**
 * Setup the focus traps. These traps will wrap focus when tabbing past the
 * panel. When shift-tabbing, the focus will stick in place.
 * @private
 */
MdPanelRef.prototype._configureTrapFocus = function() {
  // Focus doesn't remain inside of the panel without this.
  this.panelEl.attr('tabIndex', '-1');
  if (this.config['trapFocus']) {
    var element = this.panelEl;
    // Set up elements before and after the panel to capture focus and
    // redirect back into the panel.
    this._topFocusTrap = FOCUS_TRAP_TEMPLATE.clone()[0];
    this._bottomFocusTrap = FOCUS_TRAP_TEMPLATE.clone()[0];

    // When focus is about to move out of the panel, we want to intercept it
    // and redirect it back to the panel element.
    var focusHandler = function() {
      element.focus();
    };
    this._topFocusTrap.addEventListener('focus', focusHandler);
    this._bottomFocusTrap.addEventListener('focus', focusHandler);

    // Queue remove listeners function
    this._removeListeners.push(this._simpleBind(function() {
      this._topFocusTrap.removeEventListener('focus', focusHandler);
      this._bottomFocusTrap.removeEventListener('focus', focusHandler);
    }, this));

    // The top focus trap inserted immediately before the md-panel element (as
    // a sibling). The bottom focus trap inserted immediately after the
    // md-panel element (as a sibling).
    element[0].parentNode.insertBefore(this._topFocusTrap, element[0]);
    element.after(this._bottomFocusTrap);
  }
};


/**
 * Updates the animation of a panel.
 * @param {!MdPanelAnimation} animation
 */
MdPanelRef.prototype.updateAnimation = function(animation) {
  this.config['animation'] = animation;

  if (this._backdropRef) {
    this._backdropRef.config.animation.duration(animation._rawDuration);
  }
};


/**
 * Animate the panel opening.
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel has
 *     animated open.
 * @private
 */
MdPanelRef.prototype._animateOpen = function() {
  this.panelContainer.addClass('md-panel-is-showing');
  var animationConfig = this.config['animation'];
  if (!animationConfig) {
    // Promise is in progress, return it.
    this.panelContainer.addClass('_md-panel-shown');
    return this._$q.when(this);
  }

  var self = this;
  return this._$q(function(resolve) {
    var done = self._done(resolve, self);
    var warnAndOpen = function() {
      self._$log.warn(
          'mdPanel: MdPanel Animations failed. ' +
          'Showing panel without animating.');
      done();
    };

    animationConfig.animateOpen(self.panelEl)
        .then(done, warnAndOpen);
  });
};


/**
 * Animate the panel closing.
 * @returns {!angular.$q.Promise} A promise that is resolved when the panel has
 *     animated closed.
 * @private
 */
MdPanelRef.prototype._animateClose = function() {
  var animationConfig = this.config['animation'];
  if (!animationConfig) {
    this.panelContainer.removeClass('md-panel-is-showing');
    this.panelContainer.removeClass('_md-panel-shown');
    return this._$q.when(this);
  }

  var self = this;
  return this._$q(function(resolve) {
    var done = function() {
      self.panelContainer.removeClass('md-panel-is-showing');
      resolve(self);
    };
    var warnAndClose = function() {
      self._$log.warn(
          'mdPanel: MdPanel Animations failed. ' +
          'Hiding panel without animating.');
      done();
    };

    animationConfig.animateClose(self.panelEl)
        .then(done, warnAndClose);
  });
};


/**
 * Registers a interceptor with the panel. The callback should return a promise,
 * which will allow the action to continue when it gets resolved, or will
 * prevent an action if it is rejected.
 * @param {string} type Type of interceptor.
 * @param {!angular.$q.Promise<!any>} callback Callback to be registered.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.registerInterceptor = function(type, callback) {
  var error = null;

  if (!angular.isString(type)) {
    error = 'Interceptor type must be a string, instead got ' + typeof type;
  } else if (!angular.isFunction(callback)) {
    error = 'Interceptor callback must be a function, instead got ' + typeof callback;
  }

  if (error) {
    throw new Error('MdPanel: ' + error);
  }

  var interceptors = this._interceptors[type] = this._interceptors[type] || [];

  if (interceptors.indexOf(callback) === -1) {
    interceptors.push(callback);
  }

  return this;
};


/**
 * Removes a registered interceptor.
 * @param {string} type Type of interceptor to be removed.
 * @param {Function} callback Interceptor to be removed.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.removeInterceptor = function(type, callback) {
  var index = this._interceptors[type] ?
    this._interceptors[type].indexOf(callback) : -1;

  if (index > -1) {
    this._interceptors[type].splice(index, 1);
  }

  return this;
};


/**
 * Removes all interceptors.
 * @param {string=} type Type of interceptors to be removed.
 *     If ommited, all interceptors types will be removed.
 * @returns {!MdPanelRef}
 */
MdPanelRef.prototype.removeAllInterceptors = function(type) {
  if (type) {
    this._interceptors[type] = [];
  } else {
    this._interceptors = Object.create(null);
  }

  return this;
};


/**
 * Invokes all the interceptors of a certain type sequantially in
 *     reverse order. Works in a similar way to `$q.all`, except it
 *     respects the order of the functions.
 * @param {string} type Type of interceptors to be invoked.
 * @returns {!angular.$q.Promise<!MdPanelRef>}
 * @private
 */
MdPanelRef.prototype._callInterceptors = function(type) {
  var self = this;
  var $q = self._$q;
  var interceptors = self._interceptors && self._interceptors[type] || [];

  return interceptors.reduceRight(function(promise, interceptor) {
    var isPromiseLike = interceptor && angular.isFunction(interceptor.then);
    var response = isPromiseLike ? interceptor : null;

    /**
    * For interceptors to reject/cancel subsequent portions of the chain, simply
    * return a `$q.reject(<value>)`
    */
    return promise.then(function() {
      if (!response) {
        try {
          response = interceptor(self);
        } catch(e) {
          response = $q.reject(e);
        }
      }

     return response;
    });
  }, $q.resolve(self));
};


/**
 * Faster, more basic than angular.bind
 * http://jsperf.com/angular-bind-vs-custom-vs-native
 * @param {function} callback
 * @param {!Object} self
 * @return {function} Callback function with a bound self.
 */
MdPanelRef.prototype._simpleBind = function(callback, self) {
  return function(value) {
    return callback.apply(self, value);
  };
};


/**
 * @param {function} callback
 * @param {!Object} self
 * @return {function} Callback function with a self param.
 */
MdPanelRef.prototype._done = function(callback, self) {
  return function() {
    callback(self);
  };
};


/**
 * Adds a panel to a group if the panel does not exist within the group already.
 * A panel can only exist within a single group.
 * @param {string} groupName The name of the group.
 */
MdPanelRef.prototype.addToGroup = function(groupName) {
  if (!this._$mdPanel._groups[groupName]) {
    this._$mdPanel.newPanelGroup(groupName);
  }

  var group = this._$mdPanel._groups[groupName];
  var index = group.panels.indexOf(this);

  if (index < 0) {
    group.panels.push(this);
  }
};


/**
 * Removes a panel from a group if the panel exists within that group. The group
 * must be created ahead of time.
 * @param {string} groupName The name of the group.
 */
MdPanelRef.prototype.removeFromGroup = function(groupName) {
  if (!this._$mdPanel._groups[groupName]) {
    throw new Error('mdPanel: The group ' + groupName + ' does not exist.');
  }

  var group = this._$mdPanel._groups[groupName];
  var index = group.panels.indexOf(this);

  if (index > -1) {
    group.panels.splice(index, 1);
  }
};


/**
 * Possible default closeReasons for the close function.
 * @enum {string}
 */
MdPanelRef.closeReasons = {
  CLICK_OUTSIDE: 'clickOutsideToClose',
  ESCAPE: 'escapeToClose',
};


/*****************************************************************************
 *                               MdPanelPosition                             *
 *****************************************************************************/


/**
 * Position configuration object. To use, create an MdPanelPosition with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * Example:
 *
 * var panelPosition = new MdPanelPosition()
 *     .relativeTo(myButtonEl)
 *     .addPanelPosition(
 *       $mdPanel.xPosition.CENTER,
 *       $mdPanel.yPosition.ALIGN_TOPS
 *     );
 *
 * $mdPanel.create({
 *   position: panelPosition
 * });
 *
 * @param {!angular.$injector} $injector
 * @final @constructor
 */
function MdPanelPosition($injector) {
  /** @private @const {!angular.$window} */
  this._$window = $injector.get('$window');

  /** @private {boolean} */
  this._isRTL = $injector.get('$mdUtil').bidi() === 'rtl';

  /** @private @const {!angular.$mdConstant} */
  this._$mdConstant = $injector.get('$mdConstant');

  /** @private {boolean} */
  this._absolute = false;

  /** @private {!angular.JQLite} */
  this._relativeToEl;

  /** @private {string} */
  this._top = '';

  /** @private {string} */
  this._bottom = '';

  /** @private {string} */
  this._left = '';

  /** @private {string} */
  this._right = '';

  /** @private {!Array<string>} */
  this._translateX = [];

  /** @private {!Array<string>} */
  this._translateY = [];

  /** @private {!Array<{x:string, y:string}>} */
  this._positions = [];

  /** @private {?{x:string, y:string}} */
  this._actualPosition;
}


/**
 * Possible values of xPosition.
 * @enum {string}
 */
MdPanelPosition.xPosition = {
  CENTER: 'center',
  ALIGN_START: 'align-start',
  ALIGN_END: 'align-end',
  OFFSET_START: 'offset-start',
  OFFSET_END: 'offset-end'
};


/**
 * Possible values of yPosition.
 * @enum {string}
 */
MdPanelPosition.yPosition = {
  CENTER: 'center',
  ALIGN_TOPS: 'align-tops',
  ALIGN_BOTTOMS: 'align-bottoms',
  ABOVE: 'above',
  BELOW: 'below'
};


/**
 * Possible values of absolute position.
 * @enum {string}
 */
MdPanelPosition.absPosition = {
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left'
};

/**
 * Margin between the edges of a panel and the viewport.
 * @const {number}
 */
MdPanelPosition.viewportMargin = 8;


/**
 * Sets absolute positioning for the panel.
 * @return {!MdPanelPosition}
 */
MdPanelPosition.prototype.absolute = function() {
  this._absolute = true;
  return this;
};


/**
 * Sets the value of a position for the panel. Clears any previously set
 * position.
 * @param {string} position Position to set
 * @param {string=} value Value of the position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 * @private
 */
MdPanelPosition.prototype._setPosition = function(position, value) {
  if (position === MdPanelPosition.absPosition.RIGHT ||
      position === MdPanelPosition.absPosition.LEFT) {
    this._left = this._right = '';
  } else if (
      position === MdPanelPosition.absPosition.BOTTOM ||
      position === MdPanelPosition.absPosition.TOP) {
    this._top = this._bottom = '';
  } else {
    var positions = Object.keys(MdPanelPosition.absPosition).join()
        .toLowerCase();

    throw new Error('mdPanel: Position must be one of ' + positions + '.');
  }

  this['_' +  position] = angular.isString(value) ? value : '0';

  return this;
};


/**
 * Sets the value of `top` for the panel. Clears any previously set vertical
 * position.
 * @param {string=} top Value of `top`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.top = function(top) {
  return this._setPosition(MdPanelPosition.absPosition.TOP, top);
};


/**
 * Sets the value of `bottom` for the panel. Clears any previously set vertical
 * position.
 * @param {string=} bottom Value of `bottom`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.bottom = function(bottom) {
  return this._setPosition(MdPanelPosition.absPosition.BOTTOM, bottom);
};


/**
 * Sets the panel to the start of the page - `left` if `ltr` or `right` for
 * `rtl`. Clears any previously set horizontal position.
 * @param {string=} start Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.start = function(start) {
  var position = this._isRTL ? MdPanelPosition.absPosition.RIGHT : MdPanelPosition.absPosition.LEFT;
  return this._setPosition(position, start);
};


/**
 * Sets the panel to the end of the page - `right` if `ltr` or `left` for `rtl`.
 * Clears any previously set horizontal position.
 * @param {string=} end Value of position. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.end = function(end) {
  var position = this._isRTL ? MdPanelPosition.absPosition.LEFT : MdPanelPosition.absPosition.RIGHT;
  return this._setPosition(position, end);
};


/**
 * Sets the value of `left` for the panel. Clears any previously set
 * horizontal position.
 * @param {string=} left Value of `left`. Defaults to '0'.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.left = function(left) {
  return this._setPosition(MdPanelPosition.absPosition.LEFT, left);
};


/**
 * Sets the value of `right` for the panel. Clears any previously set
 * horizontal position.
 * @param {string=} right Value of `right`. Defaults to '0'.
 * @returns {!MdPanelPosition}
*/
MdPanelPosition.prototype.right = function(right) {
  return this._setPosition(MdPanelPosition.absPosition.RIGHT, right);
};


/**
 * Centers the panel horizontally in the viewport. Clears any previously set
 * horizontal position.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.centerHorizontally = function() {
  this._left = '50%';
  this._right = '';
  this._translateX = ['-50%'];
  return this;
};


/**
 * Centers the panel vertically in the viewport. Clears any previously set
 * vertical position.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.centerVertically = function() {
  this._top = '50%';
  this._bottom = '';
  this._translateY = ['-50%'];
  return this;
};


/**
 * Centers the panel horizontally and vertically in the viewport. This is
 * equivalent to calling both `centerHorizontally` and `centerVertically`.
 * Clears any previously set horizontal and vertical positions.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.center = function() {
  return this.centerHorizontally().centerVertically();
};


/**
 * Sets element for relative positioning.
 * @param {string|!Element|!angular.JQLite} element Query selector, DOM element,
 *     or angular element to set the panel relative to.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.relativeTo = function(element) {
  this._absolute = false;
  this._relativeToEl = getElement(element);
  return this;
};


/**
 * Sets the x and y positions for the panel relative to another element.
 * @param {string} xPosition must be one of the MdPanelPosition.xPosition
 *     values.
 * @param {string} yPosition must be one of the MdPanelPosition.yPosition
 *     values.
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.addPanelPosition = function(xPosition, yPosition) {
  if (!this._relativeToEl) {
    throw new Error('mdPanel: addPanelPosition can only be used with ' +
        'relative positioning. Set relativeTo first.');
  }

  this._validateXPosition(xPosition);
  this._validateYPosition(yPosition);

  this._positions.push({
      x: xPosition,
      y: yPosition,
  });
  return this;
};


/**
 * Ensures that yPosition is a valid position name. Throw an exception if not.
 * @param {string} yPosition
 */
MdPanelPosition.prototype._validateYPosition = function(yPosition) {
  // empty is ok
  if (yPosition == null) {
      return;
  }

  var positionKeys = Object.keys(MdPanelPosition.yPosition);
  var positionValues = [];
  for (var key, i = 0; key = positionKeys[i]; i++) {
    var position = MdPanelPosition.yPosition[key];
    positionValues.push(position);

    if (position === yPosition) {
      return;
    }
  }

  throw new Error('mdPanel: Panel y position only accepts the following ' +
      'values:\n' + positionValues.join(' | '));
};


/**
 * Ensures that xPosition is a valid position name. Throw an exception if not.
 * @param {string} xPosition
 */
MdPanelPosition.prototype._validateXPosition = function(xPosition) {
  // empty is ok
  if (xPosition == null) {
      return;
  }

  var positionKeys = Object.keys(MdPanelPosition.xPosition);
  var positionValues = [];
  for (var key, i = 0; key = positionKeys[i]; i++) {
    var position = MdPanelPosition.xPosition[key];
    positionValues.push(position);
    if (position === xPosition) {
      return;
    }
  }

  throw new Error('mdPanel: Panel x Position only accepts the following ' +
      'values:\n' + positionValues.join(' | '));
};


/**
 * Sets the value of the offset in the x-direction. This will add to any
 * previously set offsets.
 * @param {string|function(MdPanelPosition): string} offsetX
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.withOffsetX = function(offsetX) {
  this._translateX.push(offsetX);
  return this;
};


/**
 * Sets the value of the offset in the y-direction. This will add to any
 * previously set offsets.
 * @param {string|function(MdPanelPosition): string} offsetY
 * @returns {!MdPanelPosition}
 */
MdPanelPosition.prototype.withOffsetY = function(offsetY) {
  this._translateY.push(offsetY);
  return this;
};


/**
 * Gets the value of `top` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getTop = function() {
  return this._top;
};


/**
 * Gets the value of `bottom` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getBottom = function() {
  return this._bottom;
};


/**
 * Gets the value of `left` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getLeft = function() {
  return this._left;
};


/**
 * Gets the value of `right` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getRight = function() {
  return this._right;
};


/**
 * Gets the value of `transform` for the panel.
 * @returns {string}
 */
MdPanelPosition.prototype.getTransform = function() {
  var translateX = this._reduceTranslateValues('translateX', this._translateX);
  var translateY = this._reduceTranslateValues('translateY', this._translateY);

  // It's important to trim the result, because the browser will ignore the set
  // operation if the string contains only whitespace.
  return (translateX + ' ' + translateY).trim();
};


/**
 * Sets the `transform` value for a panel element.
 * @param {!angular.JQLite} panelEl
 * @returns {!angular.JQLite}
 * @private
 */
MdPanelPosition.prototype._setTransform = function(panelEl) {
  return panelEl.css(this._$mdConstant.CSS.TRANSFORM, this.getTransform());
};


/**
 * True if the panel is completely on-screen with this positioning; false
 * otherwise.
 * @param {!angular.JQLite} panelEl
 * @return {boolean}
 * @private
 */
MdPanelPosition.prototype._isOnscreen = function(panelEl) {
  // this works because we always use fixed positioning for the panel,
  // which is relative to the viewport.
  var left = parseInt(this.getLeft());
  var top = parseInt(this.getTop());

  if (this._translateX.length || this._translateY.length) {
    var prefixedTransform = this._$mdConstant.CSS.TRANSFORM;
    var offsets = getComputedTranslations(panelEl, prefixedTransform);
    left += offsets.x;
    top += offsets.y;
  }

  var right = left + panelEl[0].offsetWidth;
  var bottom = top + panelEl[0].offsetHeight;

  return (left >= 0) &&
    (top >= 0) &&
    (bottom <= this._$window.innerHeight) &&
    (right <= this._$window.innerWidth);
};


/**
 * Gets the first x/y position that can fit on-screen.
 * @returns {{x: string, y: string}}
 */
MdPanelPosition.prototype.getActualPosition = function() {
  return this._actualPosition;
};


/**
 * Reduces a list of translate values to a string that can be used within
 * transform.
 * @param {string} translateFn
 * @param {!Array<string>} values
 * @returns {string}
 * @private
 */
MdPanelPosition.prototype._reduceTranslateValues =
    function(translateFn, values) {
      return values.map(function(translation) {
        // TODO(crisbeto): this should add the units after #9609 is merged.
        var translationValue = angular.isFunction(translation) ?
            translation(this) : translation;
        return translateFn + '(' + translationValue + ')';
      }, this).join(' ');
    };


/**
 * Sets the panel position based on the created panel element and best x/y
 * positioning.
 * @param {!angular.JQLite} panelEl
 * @private
 */
MdPanelPosition.prototype._setPanelPosition = function(panelEl) {
  // Remove the "position adjusted" class in case it has been added before.
  panelEl.removeClass('_md-panel-position-adjusted');

  // Only calculate the position if necessary.
  if (this._absolute) {
    this._setTransform(panelEl);
    return;
  }

  if (this._actualPosition) {
    this._calculatePanelPosition(panelEl, this._actualPosition);
    this._setTransform(panelEl);
    this._constrainToViewport(panelEl);
    return;
  }

  for (var i = 0; i < this._positions.length; i++) {
    this._actualPosition = this._positions[i];
    this._calculatePanelPosition(panelEl, this._actualPosition);
    this._setTransform(panelEl);

    if (this._isOnscreen(panelEl)) {
      return;
    }
  }

  this._constrainToViewport(panelEl);
};


/**
 * Constrains a panel's position to the viewport.
 * @param {!angular.JQLite} panelEl
 * @private
 */
MdPanelPosition.prototype._constrainToViewport = function(panelEl) {
  var margin = MdPanelPosition.viewportMargin;
  var initialTop = this._top;
  var initialLeft = this._left;

  if (this.getTop()) {
    var top = parseInt(this.getTop());
    var bottom = panelEl[0].offsetHeight + top;
    var viewportHeight = this._$window.innerHeight;

    if (top < margin) {
      this._top = margin + 'px';
    } else if (bottom > viewportHeight) {
      this._top = top - (bottom - viewportHeight + margin) + 'px';
    }
  }

  if (this.getLeft()) {
    var left = parseInt(this.getLeft());
    var right = panelEl[0].offsetWidth + left;
    var viewportWidth = this._$window.innerWidth;

    if (left < margin) {
      this._left = margin + 'px';
    } else if (right > viewportWidth) {
      this._left = left - (right - viewportWidth + margin) + 'px';
    }
  }

  // Class that can be used to re-style the panel if it was repositioned.
  panelEl.toggleClass(
    '_md-panel-position-adjusted',
    this._top !== initialTop || this._left !== initialLeft
  );
};


/**
 * Switches between 'start' and 'end'.
 * @param {string} position Horizontal position of the panel
 * @returns {string} Reversed position
 * @private
 */
MdPanelPosition.prototype._reverseXPosition = function(position) {
  if (position === MdPanelPosition.xPosition.CENTER) {
    return position;
  }

  var start = 'start';
  var end = 'end';

  return position.indexOf(start) > -1 ? position.replace(start, end) : position.replace(end, start);
};


/**
 * Handles horizontal positioning in rtl or ltr environments.
 * @param {string} position Horizontal position of the panel
 * @returns {string} The correct position according the page direction
 * @private
 */
MdPanelPosition.prototype._bidi = function(position) {
  return this._isRTL ? this._reverseXPosition(position) : position;
};


/**
 * Calculates the panel position based on the created panel element and the
 * provided positioning.
 * @param {!angular.JQLite} panelEl
 * @param {!{x:string, y:string}} position
 * @private
 */
MdPanelPosition.prototype._calculatePanelPosition = function(panelEl, position) {

  var panelBounds = panelEl[0].getBoundingClientRect();
  var panelWidth = panelBounds.width;
  var panelHeight = panelBounds.height;

  var targetBounds = this._relativeToEl[0].getBoundingClientRect();

  var targetLeft = targetBounds.left;
  var targetRight = targetBounds.right;
  var targetWidth = targetBounds.width;

  switch (this._bidi(position.x)) {
    case MdPanelPosition.xPosition.OFFSET_START:
      this._left = targetLeft - panelWidth + 'px';
      break;
    case MdPanelPosition.xPosition.ALIGN_END:
      this._left = targetRight - panelWidth + 'px';
      break;
    case MdPanelPosition.xPosition.CENTER:
      var left = targetLeft + (0.5 * targetWidth) - (0.5 * panelWidth);
      this._left = left + 'px';
      break;
    case MdPanelPosition.xPosition.ALIGN_START:
      this._left = targetLeft + 'px';
      break;
    case MdPanelPosition.xPosition.OFFSET_END:
      this._left = targetRight + 'px';
      break;
  }

  var targetTop = targetBounds.top;
  var targetBottom = targetBounds.bottom;
  var targetHeight = targetBounds.height;

  switch (position.y) {
    case MdPanelPosition.yPosition.ABOVE:
      this._top = targetTop - panelHeight + 'px';
      break;
    case MdPanelPosition.yPosition.ALIGN_BOTTOMS:
      this._top = targetBottom - panelHeight + 'px';
      break;
    case MdPanelPosition.yPosition.CENTER:
      var top = targetTop + (0.5 * targetHeight) - (0.5 * panelHeight);
      this._top = top + 'px';
      break;
    case MdPanelPosition.yPosition.ALIGN_TOPS:
      this._top = targetTop + 'px';
      break;
    case MdPanelPosition.yPosition.BELOW:
      this._top = targetBottom + 'px';
      break;
  }
};


/*****************************************************************************
 *                               MdPanelAnimation                            *
 *****************************************************************************/


/**
 * Animation configuration object. To use, create an MdPanelAnimation with the
 * desired properties, then pass the object as part of $mdPanel creation.
 *
 * Example:
 *
 * var panelAnimation = new MdPanelAnimation()
 *     .openFrom(myButtonEl)
 *     .closeTo('.my-button')
 *     .withAnimation($mdPanel.animation.SCALE);
 *
 * $mdPanel.create({
 *   animation: panelAnimation
 * });
 *
 * @param {!angular.$injector} $injector
 * @final @constructor
 */
function MdPanelAnimation($injector) {
  /** @private @const {!angular.$mdUtil} */
  this._$mdUtil = $injector.get('$mdUtil');

  /**
   * @private {{element: !angular.JQLite|undefined, bounds: !DOMRect}|
   *     undefined}
   */
  this._openFrom;

  /**
   * @private {{element: !angular.JQLite|undefined, bounds: !DOMRect}|
   *     undefined}
   */
  this._closeTo;

  /** @private {string|{open: string, close: string}} */
  this._animationClass = '';

  /** @private {number} */
  this._openDuration;

  /** @private {number} */
  this._closeDuration;

  /** @private {number|{open: number, close: number}} */
  this._rawDuration;
}


/**
 * Possible default animations.
 * @enum {string}
 */
MdPanelAnimation.animation = {
  SLIDE: 'md-panel-animate-slide',
  SCALE: 'md-panel-animate-scale',
  FADE: 'md-panel-animate-fade'
};


/**
 * Specifies where to start the open animation. `openFrom` accepts a
 * click event object, query selector, DOM element, or a Rect object that
 * is used to determine the bounds. When passed a click event, the location
 * of the click will be used as the position to start the animation.
 * @param {string|!Element|!Event|{top: number, left: number}} openFrom
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.openFrom = function(openFrom) {
  // Check if 'openFrom' is an Event.
  openFrom = openFrom.target ? openFrom.target : openFrom;

  this._openFrom = this._getPanelAnimationTarget(openFrom);

  if (!this._closeTo) {
    this._closeTo = this._openFrom;
  }
  return this;
};


/**
 * Specifies where to animate the panel close. `closeTo` accepts a
 * query selector, DOM element, or a Rect object that is used to determine
 * the bounds.
 * @param {string|!Element|{top: number, left: number}} closeTo
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.closeTo = function(closeTo) {
  this._closeTo = this._getPanelAnimationTarget(closeTo);
  return this;
};


/**
 * Specifies the duration of the animation in milliseconds.
 * @param {number|{open: number, close: number}} duration
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.duration = function(duration) {
  if (duration) {
    if (angular.isNumber(duration)) {
      this._openDuration = this._closeDuration = toSeconds(duration);
    } else if (angular.isObject(duration)) {
      this._openDuration = toSeconds(duration.open);
      this._closeDuration = toSeconds(duration.close);
    }
  }

  // Save the original value so it can be passed to the backdrop.
  this._rawDuration = duration;

  return this;

  function toSeconds(value) {
    if (angular.isNumber(value)) return value / 1000;
  }
};


/**
 * Returns the element and bounds for the animation target.
 * @param {string|!Element|{top: number, left: number}} location
 * @returns {{element: !angular.JQLite|undefined, bounds: !DOMRect}}
 * @private
 */
MdPanelAnimation.prototype._getPanelAnimationTarget = function(location) {
  if (angular.isDefined(location.top) || angular.isDefined(location.left)) {
    return {
      element: undefined,
      bounds: {
        top: location.top || 0,
        left: location.left || 0
      }
    };
  } else {
    return this._getBoundingClientRect(getElement(location));
  }
};


/**
 * Specifies the animation class.
 *
 * There are several default animations that can be used:
 * (MdPanelAnimation.animation)
 *   SLIDE: The panel slides in and out from the specified
 *        elements.
 *   SCALE: The panel scales in and out.
 *   FADE: The panel fades in and out.
 *
 * @param {string|{open: string, close: string}} cssClass
 * @returns {!MdPanelAnimation}
 */
MdPanelAnimation.prototype.withAnimation = function(cssClass) {
  this._animationClass = cssClass;
  return this;
};


/**
 * Animate the panel open.
 * @param {!angular.JQLite} panelEl
 * @returns {!angular.$q.Promise} A promise that is resolved when the open
 *     animation is complete.
 */
MdPanelAnimation.prototype.animateOpen = function(panelEl) {
  var animator = this._$mdUtil.dom.animator;

  this._fixBounds(panelEl);
  var animationOptions = {};

  // Include the panel transformations when calculating the animations.
  var panelTransform = panelEl[0].style.transform || '';

  var openFrom = animator.toTransformCss(panelTransform);
  var openTo = animator.toTransformCss(panelTransform);

  switch (this._animationClass) {
    case MdPanelAnimation.animation.SLIDE:
      // Slide should start with opacity: 1.
      panelEl.css('opacity', '1');

      animationOptions = {
        transitionInClass: '_md-panel-animate-enter'
      };

      var openSlide = animator.calculateSlideToOrigin(
              panelEl, this._openFrom) || '';
      openFrom = animator.toTransformCss(openSlide + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.SCALE:
      animationOptions = {
        transitionInClass: '_md-panel-animate-enter'
      };

      var openScale = animator.calculateZoomToOrigin(
              panelEl, this._openFrom) || '';
      openFrom = animator.toTransformCss(openScale + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.FADE:
      animationOptions = {
        transitionInClass: '_md-panel-animate-enter'
      };
      break;

    default:
      if (angular.isString(this._animationClass)) {
        animationOptions = {
          transitionInClass: this._animationClass
        };
      } else {
        animationOptions = {
          transitionInClass: this._animationClass['open'],
          transitionOutClass: this._animationClass['close'],
        };
      }
  }

  animationOptions.duration = this._openDuration;

  return animator
      .translate3d(panelEl, openFrom, openTo, animationOptions);
};


/**
 * Animate the panel close.
 * @param {!angular.JQLite} panelEl
 * @returns {!angular.$q.Promise} A promise that resolves when the close
 *     animation is complete.
 */
MdPanelAnimation.prototype.animateClose = function(panelEl) {
  var animator = this._$mdUtil.dom.animator;
  var reverseAnimationOptions = {};

  // Include the panel transformations when calculating the animations.
  var panelTransform = panelEl[0].style.transform || '';

  var closeFrom = animator.toTransformCss(panelTransform);
  var closeTo = animator.toTransformCss(panelTransform);

  switch (this._animationClass) {
    case MdPanelAnimation.animation.SLIDE:
      // Slide should start with opacity: 1.
      panelEl.css('opacity', '1');
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-leave'
      };

      var closeSlide = animator.calculateSlideToOrigin(
              panelEl, this._closeTo) || '';
      closeTo = animator.toTransformCss(closeSlide + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.SCALE:
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-scale-out _md-panel-animate-leave'
      };

      var closeScale = animator.calculateZoomToOrigin(
              panelEl, this._closeTo) || '';
      closeTo = animator.toTransformCss(closeScale + ' ' + panelTransform);
      break;

    case MdPanelAnimation.animation.FADE:
      reverseAnimationOptions = {
        transitionInClass: '_md-panel-animate-fade-out _md-panel-animate-leave'
      };
      break;

    default:
      if (angular.isString(this._animationClass)) {
        reverseAnimationOptions = {
          transitionOutClass: this._animationClass
        };
      } else {
        reverseAnimationOptions = {
          transitionInClass: this._animationClass['close'],
          transitionOutClass: this._animationClass['open']
        };
      }
  }

  reverseAnimationOptions.duration = this._closeDuration;

  return animator
      .translate3d(panelEl, closeFrom, closeTo, reverseAnimationOptions);
};


/**
 * Set the height and width to match the panel if not provided.
 * @param {!angular.JQLite} panelEl
 * @private
 */
MdPanelAnimation.prototype._fixBounds = function(panelEl) {
  var panelWidth = panelEl[0].offsetWidth;
  var panelHeight = panelEl[0].offsetHeight;

  if (this._openFrom && this._openFrom.bounds.height == null) {
    this._openFrom.bounds.height = panelHeight;
  }
  if (this._openFrom && this._openFrom.bounds.width == null) {
    this._openFrom.bounds.width = panelWidth;
  }
  if (this._closeTo && this._closeTo.bounds.height == null) {
    this._closeTo.bounds.height = panelHeight;
  }
  if (this._closeTo && this._closeTo.bounds.width == null) {
    this._closeTo.bounds.width = panelWidth;
  }
};


/**
 * Identify the bounding RECT for the target element.
 * @param {!angular.JQLite} element
 * @returns {{element: !angular.JQLite|undefined, bounds: !DOMRect}}
 * @private
 */
MdPanelAnimation.prototype._getBoundingClientRect = function(element) {
  if (element instanceof angular.element) {
    return {
      element: element,
      bounds: element[0].getBoundingClientRect()
    };
  }
};


/*****************************************************************************
 *                                Util Methods                               *
 *****************************************************************************/


/**
 * Returns the angular element associated with a css selector or element.
 * @param el {string|!angular.JQLite|!Element}
 * @returns {!angular.JQLite}
 */
function getElement(el) {
  var queryResult = angular.isString(el) ?
      document.querySelector(el) : el;
  return angular.element(queryResult);
}


/**
 * Gets the computed values for an element's translateX and translateY in px.
 * @param {!angular.JQLite|!Element} el
 * @param {string} property
 * @return {{x: number, y: number}}
 */
function getComputedTranslations(el, property) {
  // The transform being returned by `getComputedStyle` is in the format:
  // `matrix(a, b, c, d, translateX, translateY)` if defined and `none`
  // if the element doesn't have a transform.
  var transform = getComputedStyle(el[0] || el)[property];
  var openIndex = transform.indexOf('(');
  var closeIndex = transform.lastIndexOf(')');
  var output = { x: 0, y: 0 };

  if (openIndex > -1 && closeIndex > -1) {
    var parsedValues = transform
      .substring(openIndex + 1, closeIndex)
      .split(', ')
      .slice(-2);

    output.x = parseInt(parsedValues[0]);
    output.y = parseInt(parsedValues[1]);
  }

  return output;
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.progressCircular
 * @description Module for a circular progressbar
 */

angular.module('material.components.progressCircular', ['material.core']);

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.progressLinear
 * @description Linear Progress module!
 */
MdProgressLinearDirective.$inject = ["$mdTheming", "$mdUtil", "$log"];
angular.module('material.components.progressLinear', [
  'material.core'
])
  .directive('mdProgressLinear', MdProgressLinearDirective);

/**
 * @ngdoc directive
 * @name mdProgressLinear
 * @module material.components.progressLinear
 * @restrict E
 *
 * @description
 * The linear progress directive is used to make loading content
 * in your app as delightful and painless as possible by minimizing
 * the amount of visual change a user sees before they can view
 * and interact with content.
 *
 * Each operation should only be represented by one activity indicator
 * For example: one refresh operation should not display both a
 * refresh bar and an activity circle.
 *
 * For operations where the percentage of the operation completed
 * can be determined, use a determinate indicator. They give users
 * a quick sense of how long an operation will take.
 *
 * For operations where the user is asked to wait a moment while
 * something finishes up, and it’s not necessary to expose what's
 * happening behind the scenes and how long it will take, use an
 * indeterminate indicator.
 *
 * @param {string} md-mode Select from one of four modes: determinate, indeterminate, buffer or query.
 *
 * Note: if the `md-mode` value is set as undefined or specified as 1 of the four (4) valid modes, then `indeterminate`
 * will be auto-applied as the mode.
 *
 * Note: if not configured, the `md-mode="indeterminate"` will be auto injected as an attribute. If `value=""` is also specified, however,
 * then `md-mode="determinate"` would be auto-injected instead.
 * @param {number=} value In determinate and buffer modes, this number represents the percentage of the primary progress bar. Default: 0
 * @param {number=} md-buffer-value In the buffer mode, this number represents the percentage of the secondary progress bar. Default: 0
 * @param {boolean=} ng-disabled Determines whether to disable the progress element.
 *
 * @usage
 * <hljs lang="html">
 * <md-progress-linear md-mode="determinate" value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="determinate" ng-value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="indeterminate"></md-progress-linear>
 *
 * <md-progress-linear md-mode="buffer" value="..." md-buffer-value="..."></md-progress-linear>
 *
 * <md-progress-linear md-mode="query"></md-progress-linear>
 * </hljs>
 */
function MdProgressLinearDirective($mdTheming, $mdUtil, $log) {
  var MODE_DETERMINATE = "determinate";
  var MODE_INDETERMINATE = "indeterminate";
  var MODE_BUFFER = "buffer";
  var MODE_QUERY = "query";
  var DISABLED_CLASS = "_md-progress-linear-disabled";

  return {
    restrict: 'E',
    template: '<div class="md-container">' +
      '<div class="md-dashed"></div>' +
      '<div class="md-bar md-bar1"></div>' +
      '<div class="md-bar md-bar2"></div>' +
      '</div>',
    compile: compile
  };

  function compile(tElement, tAttrs, transclude) {
    tElement.attr('aria-valuemin', 0);
    tElement.attr('aria-valuemax', 100);
    tElement.attr('role', 'progressbar');

    return postLink;
  }
  function postLink(scope, element, attr) {
    $mdTheming(element);

    var lastMode;
    var isDisabled = attr.hasOwnProperty('disabled');
    var toVendorCSS = $mdUtil.dom.animator.toCss;
    var bar1 = angular.element(element[0].querySelector('.md-bar1'));
    var bar2 = angular.element(element[0].querySelector('.md-bar2'));
    var container = angular.element(element[0].querySelector('.md-container'));

    element
      .attr('md-mode', mode())
      .toggleClass(DISABLED_CLASS, isDisabled);

    validateMode();
    watchAttributes();

    /**
     * Watch the value, md-buffer-value, and md-mode attributes
     */
    function watchAttributes() {
      attr.$observe('value', function(value) {
        var percentValue = clamp(value);
        element.attr('aria-valuenow', percentValue);

        if (mode() != MODE_QUERY) animateIndicator(bar2, percentValue);
      });

      attr.$observe('mdBufferValue', function(value) {
        animateIndicator(bar1, clamp(value));
      });

      attr.$observe('disabled', function(value) {
        if (value === true || value === false) {
          isDisabled = !!value;
        } else {
          isDisabled = angular.isDefined(value);
        }

        element.toggleClass(DISABLED_CLASS, isDisabled);
        container.toggleClass(lastMode, !isDisabled);
      });

      attr.$observe('mdMode', function(mode) {
        if (lastMode) container.removeClass( lastMode );

        switch( mode ) {
          case MODE_QUERY:
          case MODE_BUFFER:
          case MODE_DETERMINATE:
          case MODE_INDETERMINATE:
            container.addClass( lastMode = "md-mode-" + mode );
            break;
          default:
            container.addClass( lastMode = "md-mode-" + MODE_INDETERMINATE );
            break;
        }
      });
    }

    /**
     * Auto-defaults the mode to either `determinate` or `indeterminate` mode; if not specified
     */
    function validateMode() {
      if ( angular.isUndefined(attr.mdMode) ) {
        var hasValue = angular.isDefined(attr.value);
        var mode = hasValue ? MODE_DETERMINATE : MODE_INDETERMINATE;
        var info = "Auto-adding the missing md-mode='{0}' to the ProgressLinear element";

        //$log.debug( $mdUtil.supplant(info, [mode]) );

        element.attr("md-mode", mode);
        attr.mdMode = mode;
      }
    }

    /**
     * Is the md-mode a valid option?
     */
    function mode() {
      var value = (attr.mdMode || "").trim();
      if ( value ) {
        switch(value) {
          case MODE_DETERMINATE:
          case MODE_INDETERMINATE:
          case MODE_BUFFER:
          case MODE_QUERY:
            break;
          default:
            value = MODE_INDETERMINATE;
            break;
        }
      }
      return value;
    }

    /**
     * Manually set CSS to animate the Determinate indicator based on the specified
     * percentage value (0-100).
     */
    function animateIndicator(target, value) {
      if ( isDisabled || !mode() ) return;

      var to = $mdUtil.supplant("translateX({0}%) scale({1},1)", [ (value-100)/2, value/100 ]);
      var styles = toVendorCSS({ transform : to });
      angular.element(target).css( styles );
    }
  }

  /**
   * Clamps the value to be between 0 and 100.
   * @param {number} value The value to clamp.
   * @returns {number}
   */
  function clamp(value) {
    return Math.max(0, Math.min(value || 0, 100));
  }
}


})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.radioButton
 * @description radioButton module!
 */
mdRadioGroupDirective.$inject = ["$mdUtil", "$mdConstant", "$mdTheming", "$timeout"];
mdRadioButtonDirective.$inject = ["$mdAria", "$mdUtil", "$mdTheming"];
angular.module('material.components.radioButton', [
  'material.core'
])
  .directive('mdRadioGroup', mdRadioGroupDirective)
  .directive('mdRadioButton', mdRadioButtonDirective);

/**
 * @ngdoc directive
 * @module material.components.radioButton
 * @name mdRadioGroup
 *
 * @restrict E
 *
 * @description
 * The `<md-radio-group>` directive identifies a grouping
 * container for the 1..n grouped radio buttons; specified using nested
 * `<md-radio-button>` tags.
 *
 * As per the [material design spec](http://www.google.com/design/spec/style/color.html#color-ui-color-application)
 * the radio button is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * Note: `<md-radio-group>` and `<md-radio-button>` handle tabindex differently
 * than the native `<input type='radio'>` controls. Whereas the native controls
 * force the user to tab through all the radio buttons, `<md-radio-group>`
 * is focusable, and by default the `<md-radio-button>`s are not.
 *
 * @param {string} ng-model Assignable angular expression to data-bind to.
 * @param {boolean=} md-no-ink Use of attribute indicates flag to disable ink ripple effects.
 * @param {string} ngModel Assignable angular expression to data-bind to.
 * @param {string=} ngChange AngularJS expression to be executed when input changes due to user
 *    interaction with the input element.
 *
 * @usage
 * <hljs lang="html">
 * <md-radio-group ng-model="selected">
 *
 *   <md-radio-button
 *        ng-repeat="d in colorOptions"
 *        ng-value="d.value" aria-label="{{ d.label }}">
 *
 *          {{ d.label }}
 *
 *   </md-radio-button>
 *
 * </md-radio-group>
 * </hljs>
 *
 */
function mdRadioGroupDirective($mdUtil, $mdConstant, $mdTheming, $timeout) {
  RadioGroupController.prototype = createRadioGroupControllerProto();

  return {
    restrict: 'E',
    controller: ['$element', RadioGroupController],
    require: ['mdRadioGroup', '?ngModel'],
    link: { pre: linkRadioGroup }
  };

  function linkRadioGroup(scope, element, attr, ctrls) {
    element.addClass('_md');     // private md component indicator for styling
    $mdTheming(element);

    var rgCtrl = ctrls[0];
    var ngModelCtrl = ctrls[1] || $mdUtil.fakeNgModel();

    rgCtrl.init(ngModelCtrl);

    scope.mouseActive = false;

    element
      .attr({
        'role': 'radiogroup',
        'tabIndex': element.attr('tabindex') || '0'
      })
      .on('keydown', keydownListener)
      .on('mousedown', function(event) {
        scope.mouseActive = true;
        $timeout(function() {
          scope.mouseActive = false;
        }, 100);
      })
      .on('focus', function() {
        if(scope.mouseActive === false) {
          rgCtrl.$element.addClass('md-focused');
        }
      })
      .on('blur', function() {
        rgCtrl.$element.removeClass('md-focused');
      });

    /**
     *
     */
    function setFocus() {
      if (!element.hasClass('md-focused')) { element.addClass('md-focused'); }
    }

    /**
     *
     */
    function keydownListener(ev) {
      var keyCode = ev.which || ev.keyCode;

      // Only listen to events that we originated ourselves
      // so that we don't trigger on things like arrow keys in
      // inputs.

      if (keyCode != $mdConstant.KEY_CODE.ENTER &&
          ev.currentTarget != ev.target) {
        return;
      }

      switch (keyCode) {
        case $mdConstant.KEY_CODE.LEFT_ARROW:
        case $mdConstant.KEY_CODE.UP_ARROW:
          ev.preventDefault();
          rgCtrl.selectPrevious();
          setFocus();
          break;

        case $mdConstant.KEY_CODE.RIGHT_ARROW:
        case $mdConstant.KEY_CODE.DOWN_ARROW:
          ev.preventDefault();
          rgCtrl.selectNext();
          setFocus();
          break;

        case $mdConstant.KEY_CODE.ENTER:
          var form = angular.element($mdUtil.getClosest(element[0], 'form'));
          if (form.length > 0) {
            form.triggerHandler('submit');
          }
          break;
      }

    }
  }

  function RadioGroupController($element) {
    this._radioButtonRenderFns = [];
    this.$element = $element;
  }

  function createRadioGroupControllerProto() {
    return {
      init: function(ngModelCtrl) {
        this._ngModelCtrl = ngModelCtrl;
        this._ngModelCtrl.$render = angular.bind(this, this.render);
      },
      add: function(rbRender) {
        this._radioButtonRenderFns.push(rbRender);
      },
      remove: function(rbRender) {
        var index = this._radioButtonRenderFns.indexOf(rbRender);
        if (index !== -1) {
          this._radioButtonRenderFns.splice(index, 1);
        }
      },
      render: function() {
        this._radioButtonRenderFns.forEach(function(rbRender) {
          rbRender();
        });
      },
      setViewValue: function(value, eventType) {
        this._ngModelCtrl.$setViewValue(value, eventType);
        // update the other radio buttons as well
        this.render();
      },
      getViewValue: function() {
        return this._ngModelCtrl.$viewValue;
      },
      selectNext: function() {
        return changeSelectedButton(this.$element, 1);
      },
      selectPrevious: function() {
        return changeSelectedButton(this.$element, -1);
      },
      setActiveDescendant: function (radioId) {
        this.$element.attr('aria-activedescendant', radioId);
      },
      isDisabled: function() {
        return this.$element[0].hasAttribute('disabled');
      }
    };
  }
  /**
   * Change the radio group's selected button by a given increment.
   * If no button is selected, select the first button.
   */
  function changeSelectedButton(parent, increment) {
    // Coerce all child radio buttons into an array, then wrap then in an iterator
    var buttons = $mdUtil.iterator(parent[0].querySelectorAll('md-radio-button'), true);

    if (buttons.count()) {
      var validate = function (button) {
        // If disabled, then NOT valid
        return !angular.element(button).attr("disabled");
      };

      var selected = parent[0].querySelector('md-radio-button.md-checked');
      var target = buttons[increment < 0 ? 'previous' : 'next'](selected, validate) || buttons.first();

      // Activate radioButton's click listener (triggerHandler won't create a real click event)
      angular.element(target).triggerHandler('click');
    }
  }

}

/**
 * @ngdoc directive
 * @module material.components.radioButton
 * @name mdRadioButton
 *
 * @restrict E
 *
 * @description
 * The `<md-radio-button>`directive is the child directive required to be used within `<md-radio-group>` elements.
 *
 * While similar to the `<input type="radio" ng-model="" value="">` directive,
 * the `<md-radio-button>` directive provides ink effects, ARIA support, and
 * supports use within named radio groups.
 *
 * @param {string} ngValue AngularJS expression which sets the value to which the expression should
 *    be set when selected.
 * @param {string} value The value to which the expression should be set when selected.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {string=} aria-label Adds label to radio button for accessibility.
 * Defaults to radio button's text. If no text content is available, a warning will be logged.
 *
 * @usage
 * <hljs lang="html">
 *
 * <md-radio-button value="1" aria-label="Label 1">
 *   Label 1
 * </md-radio-button>
 *
 * <md-radio-button ng-value="specialValue" aria-label="Green">
 *   Green
 * </md-radio-button>
 *
 * </hljs>
 *
 */
function mdRadioButtonDirective($mdAria, $mdUtil, $mdTheming) {

  var CHECKED_CSS = 'md-checked';

  return {
    restrict: 'E',
    require: '^mdRadioGroup',
    transclude: true,
    template: '<div class="md-container" md-ink-ripple md-ink-ripple-checkbox>' +
                '<div class="md-off"></div>' +
                '<div class="md-on"></div>' +
              '</div>' +
              '<div ng-transclude class="md-label"></div>',
    link: link
  };

  function link(scope, element, attr, rgCtrl) {
    var lastChecked;

    $mdTheming(element);
    configureAria(element, scope);

    // ngAria overwrites the aria-checked inside a $watch for ngValue.
    // We should defer the initialization until all the watches have fired.
    // This can also be fixed by removing the `lastChecked` check, but that'll
    // cause more DOM manipulation on each digest.
    if (attr.ngValue) {
      $mdUtil.nextTick(initialize, false);
    } else {
      initialize();
    }

    /**
     * Initializes the component.
     */
    function initialize() {
      if (!rgCtrl) {
        throw 'RadioButton: No RadioGroupController could be found.';
      }

      rgCtrl.add(render);
      attr.$observe('value', render);

      element
        .on('click', listener)
        .on('$destroy', function() {
          rgCtrl.remove(render);
        });
    }

    /**
     * On click functionality.
     */
    function listener(ev) {
      if (element[0].hasAttribute('disabled') || rgCtrl.isDisabled()) return;

      scope.$apply(function() {
        rgCtrl.setViewValue(attr.value, ev && ev.type);
      });
    }

    /**
     *  Add or remove the `.md-checked` class from the RadioButton (and conditionally its parent).
     *  Update the `aria-activedescendant` attribute.
     */
    function render() {
      var checked = rgCtrl.getViewValue() == attr.value;

      if (checked === lastChecked) return;

      if (element[0].parentNode.nodeName.toLowerCase() !== 'md-radio-group') {
        // If the radioButton is inside a div, then add class so highlighting will work
        element.parent().toggleClass(CHECKED_CSS, checked);
      }

      if (checked) {
        rgCtrl.setActiveDescendant(element.attr('id'));
      }

      lastChecked = checked;

      element
        .attr('aria-checked', checked)
        .toggleClass(CHECKED_CSS, checked);
    }

    /**
     * Inject ARIA-specific attributes appropriate for each radio button
     */
    function configureAria(element, scope){
      element.attr({
        id: attr.id || 'radio_' + $mdUtil.nextUid(),
        role: 'radio',
        'aria-checked': 'false'
      });

      $mdAria.expectWithText(element, 'aria-label');
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.select
 */

/***************************************************

 ### TODO - POST RC1 ###
 - [ ] Abstract placement logic in $mdSelect service to $mdMenu service

 ***************************************************/

SelectDirective.$inject = ["$mdSelect", "$mdUtil", "$mdConstant", "$mdTheming", "$mdAria", "$parse", "$sce", "$injector"];
SelectMenuDirective.$inject = ["$parse", "$mdUtil", "$mdConstant", "$mdTheming"];
OptionDirective.$inject = ["$mdButtonInkRipple", "$mdUtil", "$mdTheming"];
SelectProvider.$inject = ["$$interimElementProvider"];
var SELECT_EDGE_MARGIN = 8;
var selectNextId = 0;
var CHECKBOX_SELECTION_INDICATOR =
  angular.element('<div class="md-container"><div class="md-icon"></div></div>');

angular.module('material.components.select', [
    'material.core',
    'material.components.backdrop'
  ])
  .directive('mdSelect', SelectDirective)
  .directive('mdSelectMenu', SelectMenuDirective)
  .directive('mdOption', OptionDirective)
  .directive('mdOptgroup', OptgroupDirective)
  .directive('mdSelectHeader', SelectHeaderDirective)
  .provider('$mdSelect', SelectProvider);

/**
 * @ngdoc directive
 * @name mdSelect
 * @restrict E
 * @module material.components.select
 *
 * @description Displays a select box, bound to an ng-model.
 *
 * When the select is required and uses a floating label, then the label will automatically contain
 * an asterisk (`*`). This behavior can be disabled by using the `md-no-asterisk` attribute.
 *
 * By default, the select will display with an underline to match other form elements. This can be
 * disabled by applying the `md-no-underline` CSS class.
 *
 * ### Option Params
 *
 * When applied, `md-option-empty` will mark the option as "empty" allowing the option to  clear the
 * select and put it back in it's default state. You may supply this attribute on any option you
 * wish, however, it is automatically applied to an option whose `value` or `ng-value` are not
 * defined.
 *
 * **Automatically Applied**
 *
 *  - `<md-option>`
 *  - `<md-option value>`
 *  - `<md-option value="">`
 *  - `<md-option ng-value>`
 *  - `<md-option ng-value="">`
 *
 * **NOT Automatically Applied**
 *
 *  - `<md-option ng-value="1">`
 *  - `<md-option ng-value="''">`
 *  - `<md-option ng-value="undefined">`
 *  - `<md-option value="undefined">` (this evaluates to the string `"undefined"`)
 *  - <code ng-non-bindable>&lt;md-option ng-value="{{someValueThatMightBeUndefined}}"&gt;</code>
 *
 * **Note:** A value of `undefined` ***is considered a valid value*** (and does not auto-apply this
 * attribute) since you may wish this to be your "Not Available" or "None" option.
 *
 * **Note:** Using the `value` attribute (as opposed to `ng-value`) always evaluates to a string, so
 * `value="null"` will require the test `ng-if="myValue != 'null'"` rather than `ng-if="!myValue"`.
 *
 * @param {expression} ng-model The model!
 * @param {boolean=} multiple When set to true, allows for more than one option to be selected. The model is an array with the selected choices.
 * @param {expression=} md-on-close Expression to be evaluated when the select is closed.
 * @param {expression=} md-on-open Expression to be evaluated when opening the select.
 * Will hide the select options and show a spinner until the evaluated promise resolves.
 * @param {expression=} md-selected-text Expression to be evaluated that will return a string
 * to be displayed as a placeholder in the select input box when it is closed. The value
 * will be treated as *text* (not html).
 * @param {expression=} md-selected-html Expression to be evaluated that will return a string
 * to be displayed as a placeholder in the select input box when it is closed. The value
 * will be treated as *html*. The value must either be explicitly marked as trustedHtml or
 * the ngSanitize module must be loaded.
 * @param {string=} placeholder Placeholder hint text.
 * @param md-no-asterisk {boolean=} When set to true, an asterisk will not be appended to the
 * floating label. **Note:** This attribute is only evaluated once; it is not watched.
 * @param {string=} aria-label Optional label for accessibility. Only necessary if no placeholder or
 * explicit label is present.
 * @param {string=} md-container-class Class list to get applied to the `.md-select-menu-container`
 * element (for custom styling).
 *
 * @usage
 * With a placeholder (label and aria-label are added dynamically)
 * <hljs lang="html">
 *   <md-input-container>
 *     <md-select
 *       ng-model="someModel"
 *       placeholder="Select a state">
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * With an explicit label
 * <hljs lang="html">
 *   <md-input-container>
 *     <label>State</label>
 *     <md-select
 *       ng-model="someModel">
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * With a select-header
 *
 * When a developer needs to put more than just a text label in the
 * md-select-menu, they should use the md-select-header.
 * The user can put custom HTML inside of the header and style it to their liking.
 * One common use case of this would be a sticky search bar.
 *
 * When using the md-select-header the labels that would previously be added to the
 * OptGroupDirective are ignored.
 *
 * <hljs lang="html">
 *   <md-input-container>
 *     <md-select ng-model="someModel">
 *       <md-select-header>
 *         <span> Neighborhoods - </span>
 *       </md-select-header>
 *       <md-option ng-value="opt" ng-repeat="opt in neighborhoods2">{{ opt }}</md-option>
 *     </md-select>
 *   </md-input-container>
 * </hljs>
 *
 * ## Selects and object equality
 * When using a `md-select` to pick from a list of objects, it is important to realize how javascript handles
 * equality. Consider the following example:
 * <hljs lang="js">
 * angular.controller('MyCtrl', function($scope) {
 *   $scope.users = [
 *     { id: 1, name: 'Bob' },
 *     { id: 2, name: 'Alice' },
 *     { id: 3, name: 'Steve' }
 *   ];
 *   $scope.selectedUser = { id: 1, name: 'Bob' };
 * });
 * </hljs>
 * <hljs lang="html">
 * <div ng-controller="MyCtrl">
 *   <md-select ng-model="selectedUser">
 *     <md-option ng-value="user" ng-repeat="user in users">{{ user.name }}</md-option>
 *   </md-select>
 * </div>
 * </hljs>
 *
 * At first one might expect that the select should be populated with "Bob" as the selected user. However,
 * this is not true. To determine whether something is selected,
 * `ngModelController` is looking at whether `$scope.selectedUser == (any user in $scope.users);`;
 *
 * Javascript's `==` operator does not check for deep equality (ie. that all properties
 * on the object are the same), but instead whether the objects are *the same object in memory*.
 * In this case, we have two instances of identical objects, but they exist in memory as unique
 * entities. Because of this, the select will have no value populated for a selected user.
 *
 * To get around this, `ngModelController` provides a `track by` option that allows us to specify a different
 * expression which will be used for the equality operator. As such, we can update our `html` to
 * make use of this by specifying the `ng-model-options="{trackBy: '$value.id'}"` on the `md-select`
 * element. This converts our equality expression to be
 * `$scope.selectedUser.id == (any id in $scope.users.map(function(u) { return u.id; }));`
 * which results in Bob being selected as desired.
 *
 * Working HTML:
 * <hljs lang="html">
 * <div ng-controller="MyCtrl">
 *   <md-select ng-model="selectedUser" ng-model-options="{trackBy: '$value.id'}">
 *     <md-option ng-value="user" ng-repeat="user in users">{{ user.name }}</md-option>
 *   </md-select>
 * </div>
 * </hljs>
 */
function SelectDirective($mdSelect, $mdUtil, $mdConstant, $mdTheming, $mdAria, $parse, $sce,
    $injector) {
  var keyCodes = $mdConstant.KEY_CODE;
  var NAVIGATION_KEYS = [keyCodes.SPACE, keyCodes.ENTER, keyCodes.UP_ARROW, keyCodes.DOWN_ARROW];

  return {
    restrict: 'E',
    require: ['^?mdInputContainer', 'mdSelect', 'ngModel', '?^form'],
    compile: compile,
    controller: function() {
    } // empty placeholder controller to be initialized in link
  };

  function compile(element, attr) {
    // add the select value that will hold our placeholder or selected option value
    var valueEl = angular.element('<md-select-value><span></span></md-select-value>');
    valueEl.append('<span class="md-select-icon" aria-hidden="true"></span>');
    valueEl.addClass('md-select-value');
    if (!valueEl[0].hasAttribute('id')) {
      valueEl.attr('id', 'select_value_label_' + $mdUtil.nextUid());
    }

    // There's got to be an md-content inside. If there's not one, let's add it.
    var mdContentEl = element.find('md-content');
    if (!mdContentEl.length) {
      element.append(angular.element('<md-content>').append(element.contents()));
    }
    mdContentEl.attr('role', 'presentation');


    // Add progress spinner for md-options-loading
    if (attr.mdOnOpen) {

      // Show progress indicator while loading async
      // Use ng-hide for `display:none` so the indicator does not interfere with the options list
      element
        .find('md-content')
        .prepend(angular.element(
          '<div>' +
          ' <md-progress-circular md-mode="indeterminate" ng-if="$$loadingAsyncDone === false" md-diameter="25px"></md-progress-circular>' +
          '</div>'
        ));

      // Hide list [of item options] while loading async
      element
        .find('md-option')
        .attr('ng-show', '$$loadingAsyncDone');
    }

    if (attr.name) {
      var autofillClone = angular.element('<select class="md-visually-hidden">');
      autofillClone.attr({
        'name': attr.name,
        'aria-hidden': 'true',
        'tabindex': '-1'
      });
      var opts = element.find('md-option');
      angular.forEach(opts, function(el) {
        var newEl = angular.element('<option>' + el.innerHTML + '</option>');
        if (el.hasAttribute('ng-value')) newEl.attr('ng-value', el.getAttribute('ng-value'));
        else if (el.hasAttribute('value')) newEl.attr('value', el.getAttribute('value'));
        autofillClone.append(newEl);
      });

      // Adds an extra option that will hold the selected value for the
      // cases where the select is a part of a non-angular form. This can be done with a ng-model,
      // however if the `md-option` is being `ng-repeat`-ed, AngularJS seems to insert a similar
      // `option` node, but with a value of `? string: <value> ?` which would then get submitted.
      // This also goes around having to prepend a dot to the name attribute.
      autofillClone.append(
        '<option ng-value="' + attr.ngModel + '" selected></option>'
      );

      element.parent().append(autofillClone);
    }

    var isMultiple = $mdUtil.parseAttributeBoolean(attr.multiple);

    // Use everything that's left inside element.contents() as the contents of the menu
    var multipleContent = isMultiple ? 'multiple' : '';
    var selectTemplate = '' +
      '<div class="md-select-menu-container" aria-hidden="true" role="presentation">' +
      '<md-select-menu role="presentation" {0}>{1}</md-select-menu>' +
      '</div>';

    selectTemplate = $mdUtil.supplant(selectTemplate, [multipleContent, element.html()]);
    element.empty().append(valueEl);
    element.append(selectTemplate);

    if(!attr.tabindex){
      attr.$set('tabindex', 0);
    }

    return function postLink(scope, element, attr, ctrls) {
      var untouched = true;
      var isDisabled, ariaLabelBase;

      var containerCtrl = ctrls[0];
      var mdSelectCtrl = ctrls[1];
      var ngModelCtrl = ctrls[2];
      var formCtrl = ctrls[3];
      // grab a reference to the select menu value label
      var valueEl = element.find('md-select-value');
      var isReadonly = angular.isDefined(attr.readonly);
      var disableAsterisk = $mdUtil.parseAttributeBoolean(attr.mdNoAsterisk);

      if (disableAsterisk) {
        element.addClass('md-no-asterisk');
      }

      if (containerCtrl) {
        var isErrorGetter = containerCtrl.isErrorGetter || function() {
          return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (formCtrl && formCtrl.$submitted));
        };

        if (containerCtrl.input) {
          // We ignore inputs that are in the md-select-header (one
          // case where this might be useful would be adding as searchbox)
          if (element.find('md-select-header').find('input')[0] !== containerCtrl.input[0]) {
            throw new Error("<md-input-container> can only have *one* child <input>, <textarea> or <select> element!");
          }
        }

        containerCtrl.input = element;
        if (!containerCtrl.label) {
          $mdAria.expect(element, 'aria-label', element.attr('placeholder'));
        }

        scope.$watch(isErrorGetter, containerCtrl.setInvalid);
      }

      var selectContainer, selectScope, selectMenuCtrl;

      findSelectContainer();
      $mdTheming(element);

      if (formCtrl && angular.isDefined(attr.multiple)) {
        $mdUtil.nextTick(function() {
          var hasModelValue = ngModelCtrl.$modelValue || ngModelCtrl.$viewValue;
          if (hasModelValue) {
            formCtrl.$setPristine();
          }
        });
      }

      var originalRender = ngModelCtrl.$render;
      ngModelCtrl.$render = function() {
        originalRender();
        syncLabelText();
        syncAriaLabel();
        inputCheckValue();
      };

      attr.$observe('placeholder', ngModelCtrl.$render);

      if (containerCtrl && containerCtrl.label) {
        attr.$observe('required', function (value) {
          // Toggle the md-required class on the input containers label, because the input container is automatically
          // applying the asterisk indicator on the label.
          containerCtrl.label.toggleClass('md-required', value && !disableAsterisk);
        });
      }

      mdSelectCtrl.setLabelText = function(text) {
        mdSelectCtrl.setIsPlaceholder(!text);

        // Whether the select label has been given via user content rather than the internal
        // template of <md-option>
        var isSelectLabelFromUser = false;

        if (attr.mdSelectedText && attr.mdSelectedHtml) {
          throw Error('md-select cannot have both `md-selected-text` and `md-selected-html`');
        }

        if (attr.mdSelectedText || attr.mdSelectedHtml) {
          text = $parse(attr.mdSelectedText || attr.mdSelectedHtml)(scope);
          isSelectLabelFromUser = true;
        } else if (!text) {
          // Use placeholder attribute, otherwise fallback to the md-input-container label
          var tmpPlaceholder = attr.placeholder ||
              (containerCtrl && containerCtrl.label ? containerCtrl.label.text() : '');

          text = tmpPlaceholder || '';
          isSelectLabelFromUser = true;
        }

        var target = valueEl.children().eq(0);

        if (attr.mdSelectedHtml) {
            // Using getTrustedHtml will run the content through $sanitize if it is not already
            // explicitly trusted. If the ngSanitize module is not loaded, this will
            // *correctly* throw an sce error.
            target.html($sce.getTrustedHtml(text));
        } else if (isSelectLabelFromUser) {
          target.text(text);
        } else {
          // If we've reached this point, the text is not user-provided.
          target.html(text);
        }
      };

      mdSelectCtrl.setIsPlaceholder = function(isPlaceholder) {
        if (isPlaceholder) {
          valueEl.addClass('md-select-placeholder');
          if (containerCtrl && containerCtrl.label) {
            containerCtrl.label.addClass('md-placeholder');
          }
        } else {
          valueEl.removeClass('md-select-placeholder');
          if (containerCtrl && containerCtrl.label) {
            containerCtrl.label.removeClass('md-placeholder');
          }
        }
      };

      if (!isReadonly) {
        element
          .on('focus', function(ev) {
            // Always focus the container (if we have one) so floating labels and other styles are
            // applied properly
            containerCtrl && containerCtrl.setFocused(true);
          });

        // Attach before ngModel's blur listener to stop propagation of blur event
        // to prevent from setting $touched.
        element.on('blur', function(event) {
          if (untouched) {
            untouched = false;
            if (selectScope._mdSelectIsOpen) {
              event.stopImmediatePropagation();
            }
          }

          if (selectScope._mdSelectIsOpen) return;
          containerCtrl && containerCtrl.setFocused(false);
          inputCheckValue();
        });
      }

      mdSelectCtrl.triggerClose = function() {
        $parse(attr.mdOnClose)(scope);
      };

      scope.$$postDigest(function() {
        initAriaLabel();
        syncLabelText();
        syncAriaLabel();
      });

      function initAriaLabel() {
        var labelText = element.attr('aria-label') || element.attr('placeholder');
        if (!labelText && containerCtrl && containerCtrl.label) {
          labelText = containerCtrl.label.text();
        }
        ariaLabelBase = labelText;
        $mdAria.expect(element, 'aria-label', labelText);
      }

      scope.$watch(function() {
        return selectMenuCtrl.selectedLabels();
      }, syncLabelText);

      function syncLabelText() {
        if (selectContainer) {
          selectMenuCtrl = selectMenuCtrl || selectContainer.find('md-select-menu').controller('mdSelectMenu');
          mdSelectCtrl.setLabelText(selectMenuCtrl.selectedLabels());
        }
      }

      function syncAriaLabel() {
        if (!ariaLabelBase) return;
        var ariaLabels = selectMenuCtrl.selectedLabels({mode: 'aria'});
        element.attr('aria-label', ariaLabels.length ? ariaLabelBase + ': ' + ariaLabels : ariaLabelBase);
      }

      var deregisterWatcher;
      attr.$observe('ngMultiple', function(val) {
        if (deregisterWatcher) deregisterWatcher();
        var parser = $parse(val);
        deregisterWatcher = scope.$watch(function() {
          return parser(scope);
        }, function(multiple, prevVal) {
          if (multiple === undefined && prevVal === undefined) return; // assume compiler did a good job
          if (multiple) {
            element.attr('multiple', 'multiple');
          } else {
            element.removeAttr('multiple');
          }
          element.attr('aria-multiselectable', multiple ? 'true' : 'false');
          if (selectContainer) {
            selectMenuCtrl.setMultiple(multiple);
            originalRender = ngModelCtrl.$render;
            ngModelCtrl.$render = function() {
              originalRender();
              syncLabelText();
              syncAriaLabel();
              inputCheckValue();
            };
            ngModelCtrl.$render();
          }
        });
      });

      attr.$observe('disabled', function(disabled) {
        if (angular.isString(disabled)) {
          disabled = true;
        }
        // Prevent click event being registered twice
        if (isDisabled !== undefined && isDisabled === disabled) {
          return;
        }
        isDisabled = disabled;
        if (disabled) {
          element
            .attr({'aria-disabled': 'true'})
            .removeAttr('tabindex')
            .off('click', openSelect)
            .off('keydown', handleKeypress);
        } else {
          element
            .attr({'tabindex': attr.tabindex, 'aria-disabled': 'false'})
            .on('click', openSelect)
            .on('keydown', handleKeypress);
        }
      });

      if (!attr.hasOwnProperty('disabled') && !attr.hasOwnProperty('ngDisabled')) {
        element.attr({'aria-disabled': 'false'});
        element.on('click', openSelect);
        element.on('keydown', handleKeypress);
      }

      var ariaAttrs = {
        role: 'listbox',
        'aria-expanded': 'false',
        'aria-multiselectable': isMultiple && !attr.ngMultiple ? 'true' : 'false'
      };

      if (!element[0].hasAttribute('id')) {
        ariaAttrs.id = 'select_' + $mdUtil.nextUid();
      }

      var containerId = 'select_container_' + $mdUtil.nextUid();
      selectContainer.attr('id', containerId);
      // Only add aria-owns if element ownership is NOT represented in the DOM.
      if (!element.find('md-select-menu').length) {
        ariaAttrs['aria-owns'] = containerId;
      }
      element.attr(ariaAttrs);

      scope.$on('$destroy', function() {
        $mdSelect
          .destroy()
          .finally(function() {
            if (containerCtrl) {
              containerCtrl.setFocused(false);
              containerCtrl.setHasValue(false);
              containerCtrl.input = null;
            }
            ngModelCtrl.$setTouched();
          });
      });



      function inputCheckValue() {
        // The select counts as having a value if one or more options are selected,
        // or if the input's validity state says it has bad input (eg string in a number input)
        containerCtrl && containerCtrl.setHasValue(selectMenuCtrl.selectedLabels().length > 0 || (element[0].validity || {}).badInput);
      }

      function findSelectContainer() {
        selectContainer = angular.element(
          element[0].querySelector('.md-select-menu-container')
        );
        selectScope = scope;
        if (attr.mdContainerClass) {
          var value = selectContainer[0].getAttribute('class') + ' ' + attr.mdContainerClass;
          selectContainer[0].setAttribute('class', value);
        }
        selectMenuCtrl = selectContainer.find('md-select-menu').controller('mdSelectMenu');
        selectMenuCtrl.init(ngModelCtrl, attr.ngModel);
        element.on('$destroy', function() {
          selectContainer.remove();
        });
      }

      function handleKeypress(e) {
        if ($mdConstant.isNavigationKey(e)) {
          // prevent page scrolling on interaction
          e.preventDefault();
          openSelect(e);
        } else {
          if (shouldHandleKey(e, $mdConstant)) {
            e.preventDefault();

            var node = selectMenuCtrl.optNodeForKeyboardSearch(e);
            if (!node || node.hasAttribute('disabled')) return;
            var optionCtrl = angular.element(node).controller('mdOption');
            if (!selectMenuCtrl.isMultiple) {
              selectMenuCtrl.deselect(Object.keys(selectMenuCtrl.selected)[0]);
            }
            selectMenuCtrl.select(optionCtrl.hashKey, optionCtrl.value);
            selectMenuCtrl.refreshViewValue();
          }
        }
      }

      function openSelect() {
        selectScope._mdSelectIsOpen = true;
        element.attr('aria-expanded', 'true');

        $mdSelect.show({
          scope: selectScope,
          preserveScope: true,
          skipCompile: true,
          element: selectContainer,
          target: element[0],
          selectCtrl: mdSelectCtrl,
          preserveElement: true,
          hasBackdrop: true,
          loadingAsync: attr.mdOnOpen ? scope.$eval(attr.mdOnOpen) || true : false
        }).finally(function() {
          selectScope._mdSelectIsOpen = false;
          element.focus();
          element.attr('aria-expanded', 'false');
          ngModelCtrl.$setTouched();
        });
      }

    };
  }
}

function SelectMenuDirective($parse, $mdUtil, $mdConstant, $mdTheming) {
  // We want the scope to be set to 'false' so an isolated scope is not created
  // which would interfere with the md-select-header's access to the
  // parent scope.
  SelectMenuController.$inject = ["$scope", "$attrs", "$element"];
  return {
    restrict: 'E',
    require: ['mdSelectMenu'],
    scope: false,
    controller: SelectMenuController,
    link: {pre: preLink}
  };

  // We use preLink instead of postLink to ensure that the select is initialized before
  // its child options run postLink.
  function preLink(scope, element, attr, ctrls) {
    var selectCtrl = ctrls[0];

    element.addClass('_md');     // private md component indicator for styling

    $mdTheming(element);
    element.on('click', clickListener);
    element.on('keypress', keyListener);

    function keyListener(e) {
      if (e.keyCode == 13 || e.keyCode == 32) {
        clickListener(e);
      }
    }

    function clickListener(ev) {
      var option = $mdUtil.getClosest(ev.target, 'md-option');
      var optionCtrl = option && angular.element(option).data('$mdOptionController');
      if (!option || !optionCtrl) return;
      if (option.hasAttribute('disabled')) {
        ev.stopImmediatePropagation();
        return false;
      }

      var optionHashKey = selectCtrl.hashGetter(optionCtrl.value);
      var isSelected = angular.isDefined(selectCtrl.selected[optionHashKey]);

      scope.$apply(function() {
        if (selectCtrl.isMultiple) {
          if (isSelected) {
            selectCtrl.deselect(optionHashKey);
          } else {
            selectCtrl.select(optionHashKey, optionCtrl.value);
          }
        } else {
          if (!isSelected) {
            selectCtrl.deselect(Object.keys(selectCtrl.selected)[0]);
            selectCtrl.select(optionHashKey, optionCtrl.value);
          }
        }
        selectCtrl.refreshViewValue();
      });
    }
  }

  function SelectMenuController($scope, $attrs, $element) {
    var self = this;
    self.isMultiple = angular.isDefined($attrs.multiple);
    // selected is an object with keys matching all of the selected options' hashed values
    self.selected = {};
    // options is an object with keys matching every option's hash value,
    // and values matching every option's controller.
    self.options = {};

    $scope.$watchCollection(function() {
      return self.options;
    }, function() {
      self.ngModel.$render();
    });

    var deregisterCollectionWatch;
    var defaultIsEmpty;
    self.setMultiple = function(isMultiple) {
      var ngModel = self.ngModel;
      defaultIsEmpty = defaultIsEmpty || ngModel.$isEmpty;

      self.isMultiple = isMultiple;
      if (deregisterCollectionWatch) deregisterCollectionWatch();

      if (self.isMultiple) {
        ngModel.$validators['md-multiple'] = validateArray;
        ngModel.$render = renderMultiple;

        // watchCollection on the model because by default ngModel only watches the model's
        // reference. This allowed the developer to also push and pop from their array.
        $scope.$watchCollection(self.modelBinding, function(value) {
          if (validateArray(value)) renderMultiple(value);
          self.ngModel.$setPristine();
        });

        ngModel.$isEmpty = function(value) {
          return !value || value.length === 0;
        };
      } else {
        delete ngModel.$validators['md-multiple'];
        ngModel.$render = renderSingular;
      }

      function validateArray(modelValue, viewValue) {
        // If a value is truthy but not an array, reject it.
        // If value is undefined/falsy, accept that it's an empty array.
        return angular.isArray(modelValue || viewValue || []);
      }
    };

    var searchStr = '';
    var clearSearchTimeout, optNodes, optText;
    var CLEAR_SEARCH_AFTER = 300;

    self.optNodeForKeyboardSearch = function(e) {
      clearSearchTimeout && clearTimeout(clearSearchTimeout);
      clearSearchTimeout = setTimeout(function() {
        clearSearchTimeout = undefined;
        searchStr = '';
        optText = undefined;
        optNodes = undefined;
      }, CLEAR_SEARCH_AFTER);

      // Support 1-9 on numpad
      var keyCode = e.keyCode - ($mdConstant.isNumPadKey(e) ? 48 : 0);

      searchStr += String.fromCharCode(keyCode);
      var search = new RegExp('^' + searchStr, 'i');
      if (!optNodes) {
        optNodes = $element.find('md-option');
        optText = new Array(optNodes.length);
        angular.forEach(optNodes, function(el, i) {
          optText[i] = el.textContent.trim();
        });
      }
      for (var i = 0; i < optText.length; ++i) {
        if (search.test(optText[i])) {
          return optNodes[i];
        }
      }
    };

    self.init = function(ngModel, binding) {
      self.ngModel = ngModel;
      self.modelBinding = binding;

      // Setup a more robust version of isEmpty to ensure value is a valid option
      self.ngModel.$isEmpty = function($viewValue) {
        // We have to transform the viewValue into the hashKey, because otherwise the
        // OptionCtrl may not exist. Developers may have specified a trackBy function.
        return !self.options[self.hashGetter($viewValue)];
      };

      // Allow users to provide `ng-model="foo" ng-model-options="{trackBy: 'foo.id'}"` so
      // that we can properly compare objects set on the model to the available options
      var trackByOption = $mdUtil.getModelOption(ngModel, 'trackBy');

      if (trackByOption) {
        var trackByLocals = {};
        var trackByParsed = $parse(trackByOption);
        self.hashGetter = function(value, valueScope) {
          trackByLocals.$value = value;
          return trackByParsed(valueScope || $scope, trackByLocals);
        };
        // If the user doesn't provide a trackBy, we automatically generate an id for every
        // value passed in
      } else {
        self.hashGetter = function getHashValue(value) {
          if (angular.isObject(value)) {
            return 'object_' + (value.$$mdSelectId || (value.$$mdSelectId = ++selectNextId));
          }
          return value;
        };
      }
      self.setMultiple(self.isMultiple);
    };

    self.selectedLabels = function(opts) {
      opts = opts || {};
      var mode = opts.mode || 'html';
      var selectedOptionEls = $mdUtil.nodesToArray($element[0].querySelectorAll('md-option[selected]'));
      if (selectedOptionEls.length) {
        var mapFn;

        if (mode == 'html') {
          // Map the given element to its innerHTML string. If the element has a child ripple
          // container remove it from the HTML string, before returning the string.
          mapFn = function(el) {
            // If we do not have a `value` or `ng-value`, assume it is an empty option which clears the select
            if (el.hasAttribute('md-option-empty')) {
              return '';
            }

            var html = el.innerHTML;

            // Remove the ripple container from the selected option, copying it would cause a CSP violation.
            var rippleContainer = el.querySelector('.md-ripple-container');
            if (rippleContainer) {
              html = html.replace(rippleContainer.outerHTML, '');
            }

            // Remove the checkbox container, because it will cause the label to wrap inside of the placeholder.
            // It should be not displayed inside of the label element.
            var checkboxContainer = el.querySelector('.md-container');
            if (checkboxContainer) {
              html = html.replace(checkboxContainer.outerHTML, '');
            }

            return html;
          };
        } else if (mode == 'aria') {
          mapFn = function(el) { return el.hasAttribute('aria-label') ? el.getAttribute('aria-label') : el.textContent; };
        }

        // Ensure there are no duplicates; see https://github.com/angular/material/issues/9442
        return $mdUtil.uniq(selectedOptionEls.map(mapFn)).join(', ');
      } else {
        return '';
      }
    };

    self.select = function(hashKey, hashedValue) {
      var option = self.options[hashKey];
      option && option.setSelected(true);
      self.selected[hashKey] = hashedValue;
    };
    self.deselect = function(hashKey) {
      var option = self.options[hashKey];
      option && option.setSelected(false);
      delete self.selected[hashKey];
    };

    self.addOption = function(hashKey, optionCtrl) {
      if (angular.isDefined(self.options[hashKey])) {
        throw new Error('Duplicate md-option values are not allowed in a select. ' +
          'Duplicate value "' + optionCtrl.value + '" found.');
      }

      self.options[hashKey] = optionCtrl;

      // If this option's value was already in our ngModel, go ahead and select it.
      if (angular.isDefined(self.selected[hashKey])) {
        self.select(hashKey, optionCtrl.value);

        // When the current $modelValue of the ngModel Controller is using the same hash as
        // the current option, which will be added, then we can be sure, that the validation
        // of the option has occurred before the option was added properly.
        // This means, that we have to manually trigger a new validation of the current option.
        if (angular.isDefined(self.ngModel.$modelValue) && self.hashGetter(self.ngModel.$modelValue) === hashKey) {
          self.ngModel.$validate();
        }

        self.refreshViewValue();
      }
    };
    self.removeOption = function(hashKey) {
      delete self.options[hashKey];
      // Don't deselect an option when it's removed - the user's ngModel should be allowed
      // to have values that do not match a currently available option.
    };

    self.refreshViewValue = function() {
      var values = [];
      var option;
      for (var hashKey in self.selected) {
        // If this hashKey has an associated option, push that option's value to the model.
        if ((option = self.options[hashKey])) {
          values.push(option.value);
        } else {
          // Otherwise, the given hashKey has no associated option, and we got it
          // from an ngModel value at an earlier time. Push the unhashed value of
          // this hashKey to the model.
          // This allows the developer to put a value in the model that doesn't yet have
          // an associated option.
          values.push(self.selected[hashKey]);
        }
      }
      var usingTrackBy = $mdUtil.getModelOption(self.ngModel, 'trackBy');

      var newVal = self.isMultiple ? values : values[0];
      var prevVal = self.ngModel.$modelValue;

      if (usingTrackBy ? !angular.equals(prevVal, newVal) : (prevVal + '') !== newVal) {
        self.ngModel.$setViewValue(newVal);
        self.ngModel.$render();
      }
    };

    function renderMultiple() {
      var newSelectedValues = self.ngModel.$modelValue || self.ngModel.$viewValue || [];
      if (!angular.isArray(newSelectedValues)) return;

      var oldSelected = Object.keys(self.selected);

      var newSelectedHashes = newSelectedValues.map(self.hashGetter);
      var deselected = oldSelected.filter(function(hash) {
        return newSelectedHashes.indexOf(hash) === -1;
      });

      deselected.forEach(self.deselect);
      newSelectedHashes.forEach(function(hashKey, i) {
        self.select(hashKey, newSelectedValues[i]);
      });
    }

    function renderSingular() {
      var value = self.ngModel.$viewValue || self.ngModel.$modelValue;
      Object.keys(self.selected).forEach(self.deselect);
      self.select(self.hashGetter(value), value);
    }
  }

}

function OptionDirective($mdButtonInkRipple, $mdUtil, $mdTheming) {

  OptionController.$inject = ["$element"];
  return {
    restrict: 'E',
    require: ['mdOption', '^^mdSelectMenu'],
    controller: OptionController,
    compile: compile
  };

  function compile(element, attr) {
    // Manual transclusion to avoid the extra inner <span> that ng-transclude generates
    element.append(angular.element('<div class="md-text">').append(element.contents()));

    element.attr('tabindex', attr.tabindex || '0');

    if (!hasDefinedValue(attr)) {
      element.attr('md-option-empty', '');
    }

    return postLink;
  }

  function hasDefinedValue(attr) {
    var value = attr.value;
    var ngValue = attr.ngValue;

    return value || ngValue;
  }

  function postLink(scope, element, attr, ctrls) {
    var optionCtrl = ctrls[0];
    var selectCtrl = ctrls[1];

    $mdTheming(element);

    if (selectCtrl.isMultiple) {
      element.addClass('md-checkbox-enabled');
      element.prepend(CHECKBOX_SELECTION_INDICATOR.clone());
    }

    if (angular.isDefined(attr.ngValue)) {
      scope.$watch(attr.ngValue, setOptionValue);
    } else if (angular.isDefined(attr.value)) {
      setOptionValue(attr.value);
    } else {
      scope.$watch(function() {
        return element.text().trim();
      }, setOptionValue);
    }

    attr.$observe('disabled', function(disabled) {
      if (disabled) {
        element.attr('tabindex', '-1');
      } else {
        element.attr('tabindex', '0');
      }
    });

    scope.$$postDigest(function() {
      attr.$observe('selected', function(selected) {
        if (!angular.isDefined(selected)) return;
        if (typeof selected == 'string') selected = true;
        if (selected) {
          if (!selectCtrl.isMultiple) {
            selectCtrl.deselect(Object.keys(selectCtrl.selected)[0]);
          }
          selectCtrl.select(optionCtrl.hashKey, optionCtrl.value);
        } else {
          selectCtrl.deselect(optionCtrl.hashKey);
        }
        selectCtrl.refreshViewValue();
      });
    });

    $mdButtonInkRipple.attach(scope, element);
    configureAria();

    function setOptionValue(newValue, oldValue, prevAttempt) {
      if (!selectCtrl.hashGetter) {
        if (!prevAttempt) {
          scope.$$postDigest(function() {
            setOptionValue(newValue, oldValue, true);
          });
        }
        return;
      }
      var oldHashKey = selectCtrl.hashGetter(oldValue, scope);
      var newHashKey = selectCtrl.hashGetter(newValue, scope);

      optionCtrl.hashKey = newHashKey;
      optionCtrl.value = newValue;

      selectCtrl.removeOption(oldHashKey, optionCtrl);
      selectCtrl.addOption(newHashKey, optionCtrl);
    }

    scope.$on('$destroy', function() {
      selectCtrl.removeOption(optionCtrl.hashKey, optionCtrl);
    });

    function configureAria() {
      var ariaAttrs = {
        'role': 'option',
        'aria-selected': 'false'
      };

      if (!element[0].hasAttribute('id')) {
        ariaAttrs.id = 'select_option_' + $mdUtil.nextUid();
      }
      element.attr(ariaAttrs);
    }
  }

  function OptionController($element) {
    this.selected = false;
    this.setSelected = function(isSelected) {
      if (isSelected && !this.selected) {
        $element.attr({
          'selected': 'selected',
          'aria-selected': 'true'
        });
      } else if (!isSelected && this.selected) {
        $element.removeAttr('selected');
        $element.attr('aria-selected', 'false');
      }
      this.selected = isSelected;
    };
  }

}

function OptgroupDirective() {
  return {
    restrict: 'E',
    compile: compile
  };
  function compile(el, attrs) {
    // If we have a select header element, we don't want to add the normal label
    // header.
    if (!hasSelectHeader()) {
      setupLabelElement();
    }

    function hasSelectHeader() {
      return el.parent().find('md-select-header').length;
    }

    function setupLabelElement() {
      var labelElement = el.find('label');
      if (!labelElement.length) {
        labelElement = angular.element('<label>');
        el.prepend(labelElement);
      }
      labelElement.addClass('md-container-ignore');
      labelElement.attr('aria-hidden', 'true');
      if (attrs.label) labelElement.text(attrs.label);
    }
  }
}

function SelectHeaderDirective() {
  return {
    restrict: 'E',
  };
}

function SelectProvider($$interimElementProvider) {
  selectDefaultOptions.$inject = ["$mdSelect", "$mdConstant", "$mdUtil", "$window", "$q", "$$rAF", "$animateCss", "$animate", "$document"];
  return $$interimElementProvider('$mdSelect')
    .setDefaults({
      methods: ['target'],
      options: selectDefaultOptions
    });

  /* @ngInject */
  function selectDefaultOptions($mdSelect, $mdConstant, $mdUtil, $window, $q, $$rAF, $animateCss, $animate, $document) {
    var ERROR_TARGET_EXPECTED = "$mdSelect.show() expected a target element in options.target but got '{0}'!";
    var animator = $mdUtil.dom.animator;
    var keyCodes = $mdConstant.KEY_CODE;

    return {
      parent: 'body',
      themable: true,
      onShow: onShow,
      onRemove: onRemove,
      hasBackdrop: true,
      disableParentScroll: true
    };

    /**
     * Interim-element onRemove logic....
     */
    function onRemove(scope, element, opts) {
      var animationRunner = null;
      var destroyListener = scope.$on('$destroy', function() {
        // Listen for the case where the element was destroyed while there was an
        // ongoing close animation. If this happens, we need to end the animation
        // manually.
        animationRunner.end();
      });

      opts = opts || { };
      opts.cleanupInteraction();
      opts.cleanupResizing();
      opts.hideBackdrop();

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal
      return (opts.$destroy === true) ? cleanElement() : animateRemoval().then(cleanElement);

      /**
       * For normal closes (eg clicks), animate the removal.
       * For forced closes (like $destroy events from navigation),
       * skip the animations
       */
      function animateRemoval() {
        animationRunner = $animateCss(element, {addClass: 'md-leave'});
        return animationRunner.start();
      }

      /**
       * Restore the element to a closed state
       */
      function cleanElement() {
        destroyListener();

        element
          .removeClass('md-active')
          .attr('aria-hidden', 'true')
          .css('display', 'none');
        element.parent().find('md-select-value').removeAttr('aria-hidden');

        announceClosed(opts);

        if (!opts.$destroy && opts.restoreFocus) {
          opts.target.focus();
        }
      }

    }

    /**
     * Interim-element onShow logic....
     */
    function onShow(scope, element, opts) {

      watchAsyncLoad();
      sanitizeAndConfigure(scope, opts);

      opts.hideBackdrop = showBackdrop(scope, element, opts);

      return showDropDown(scope, element, opts)
        .then(function(response) {
          element.attr('aria-hidden', 'false');
          opts.alreadyOpen = true;
          opts.cleanupInteraction = activateInteraction();
          opts.cleanupResizing = activateResizing();
          autoFocus(opts.focusedNode);

          return response;
        }, opts.hideBackdrop);

      // ************************************
      // Closure Functions
      // ************************************

      /**
       *  Attach the select DOM element(s) and animate to the correct positions
       *  and scalings...
       */
      function showDropDown(scope, element, opts) {
        if (opts.parent !== element.parent()) {
          element.parent().attr('aria-owns', element.attr('id'));
        }
        element.parent().find('md-select-value').attr('aria-hidden', 'true');

        opts.parent.append(element);

        return $q(function(resolve, reject) {

          try {

            $animateCss(element, {removeClass: 'md-leave', duration: 0})
              .start()
              .then(positionAndFocusMenu)
              .then(resolve);

          } catch (e) {
            reject(e);
          }

        });
      }

      /**
       * Initialize container and dropDown menu positions/scale, then animate
       * to show.
       */
      function positionAndFocusMenu() {
        return $q(function(resolve) {
          if (opts.isRemoved) return $q.reject(false);

          var info = calculateMenuPositions(scope, element, opts);

          info.container.element.css(animator.toCss(info.container.styles));
          info.dropDown.element.css(animator.toCss(info.dropDown.styles));

          $$rAF(function() {
            element.addClass('md-active');
            info.dropDown.element.css(animator.toCss({transform: ''}));

            resolve();
          });

        });
      }

      /**
       * Show modal backdrop element...
       */
      function showBackdrop(scope, element, options) {

        // If we are not within a dialog...
        if (options.disableParentScroll && !$mdUtil.getClosest(options.target, 'MD-DIALOG')) {
          // !! DO this before creating the backdrop; since disableScrollAround()
          //    configures the scroll offset; which is used by mdBackDrop postLink()
          options.restoreScroll = $mdUtil.disableScrollAround(options.element, options.parent);
        } else {
          options.disableParentScroll = false;
        }

        if (options.hasBackdrop) {
          // Override duration to immediately show invisible backdrop
          options.backdrop = $mdUtil.createBackdrop(scope, "md-select-backdrop md-click-catcher");
          $animate.enter(options.backdrop, $document[0].body, null, {duration: 0});
        }

        /**
         * Hide modal backdrop element...
         */
        return function hideBackdrop() {
          if (options.backdrop) options.backdrop.remove();
          if (options.disableParentScroll) options.restoreScroll();

          delete options.restoreScroll;
        };
      }

      /**
       *
       */
      function autoFocus(focusedNode) {
        if (focusedNode && !focusedNode.hasAttribute('disabled')) {
          focusedNode.focus();
        }
      }

      /**
       * Check for valid opts and set some sane defaults
       */
      function sanitizeAndConfigure(scope, options) {
        var selectEl = element.find('md-select-menu');

        if (!options.target) {
          throw new Error($mdUtil.supplant(ERROR_TARGET_EXPECTED, [options.target]));
        }

        angular.extend(options, {
          isRemoved: false,
          target: angular.element(options.target), //make sure it's not a naked dom node
          parent: angular.element(options.parent),
          selectEl: selectEl,
          contentEl: element.find('md-content'),
          optionNodes: selectEl[0].getElementsByTagName('md-option')
        });
      }

      /**
       * Configure various resize listeners for screen changes
       */
      function activateResizing() {
        var debouncedOnResize = (function(scope, target, options) {

          return function() {
            if (options.isRemoved) return;

            var updates = calculateMenuPositions(scope, target, options);
            var container = updates.container;
            var dropDown = updates.dropDown;

            container.element.css(animator.toCss(container.styles));
            dropDown.element.css(animator.toCss(dropDown.styles));
          };

        })(scope, element, opts);

        var window = angular.element($window);
        window.on('resize', debouncedOnResize);
        window.on('orientationchange', debouncedOnResize);

        // Publish deactivation closure...
        return function deactivateResizing() {

          // Disable resizing handlers
          window.off('resize', debouncedOnResize);
          window.off('orientationchange', debouncedOnResize);
        };
      }

      /**
       *  If asynchronously loading, watch and update internal
       *  '$$loadingAsyncDone' flag
       */
      function watchAsyncLoad() {
        if (opts.loadingAsync && !opts.isRemoved) {
          scope.$$loadingAsyncDone = false;

          $q.when(opts.loadingAsync)
            .then(function() {
              scope.$$loadingAsyncDone = true;
              delete opts.loadingAsync;
            }).then(function() {
              $$rAF(positionAndFocusMenu);
            });
        }
      }

      /**
       *
       */
      function activateInteraction() {
        if (opts.isRemoved) return;

        var dropDown = opts.selectEl;
        var selectCtrl = dropDown.controller('mdSelectMenu') || {};

        element.addClass('md-clickable');

        // Close on backdrop click
        opts.backdrop && opts.backdrop.on('click', onBackdropClick);

        // Escape to close
        // Cycling of options, and closing on enter
        dropDown.on('keydown', onMenuKeyDown);
        dropDown.on('click', checkCloseMenu);

        return function cleanupInteraction() {
          opts.backdrop && opts.backdrop.off('click', onBackdropClick);
          dropDown.off('keydown', onMenuKeyDown);
          dropDown.off('click', checkCloseMenu);

          element.removeClass('md-clickable');
          opts.isRemoved = true;
        };

        // ************************************
        // Closure Functions
        // ************************************

        function onBackdropClick(e) {
          e.preventDefault();
          e.stopPropagation();
          opts.restoreFocus = false;
          $mdUtil.nextTick($mdSelect.hide, true);
        }

        function onMenuKeyDown(ev) {
          ev.preventDefault();
          ev.stopPropagation();

          switch (ev.keyCode) {
            case keyCodes.UP_ARROW:
              return focusPrevOption();
            case keyCodes.DOWN_ARROW:
              return focusNextOption();
            case keyCodes.SPACE:
            case keyCodes.ENTER:
              var option = $mdUtil.getClosest(ev.target, 'md-option');
              if (option) {
                dropDown.triggerHandler({
                  type: 'click',
                  target: option
                });
                ev.preventDefault();
              }
              checkCloseMenu(ev);
              break;
            case keyCodes.TAB:
            case keyCodes.ESCAPE:
              ev.stopPropagation();
              ev.preventDefault();
              opts.restoreFocus = true;
              $mdUtil.nextTick($mdSelect.hide, true);
              break;
            default:
              if (shouldHandleKey(ev, $mdConstant)) {
                var optNode = dropDown.controller('mdSelectMenu').optNodeForKeyboardSearch(ev);
                opts.focusedNode = optNode || opts.focusedNode;
                optNode && optNode.focus();
              }
          }
        }

        function focusOption(direction) {
          var optionsArray = $mdUtil.nodesToArray(opts.optionNodes);
          var index = optionsArray.indexOf(opts.focusedNode);

          var newOption;

          do {
            if (index === -1) {
              // We lost the previously focused element, reset to first option
              index = 0;
            } else if (direction === 'next' && index < optionsArray.length - 1) {
              index++;
            } else if (direction === 'prev' && index > 0) {
              index--;
            }
            newOption = optionsArray[index];
            if (newOption.hasAttribute('disabled')) newOption = undefined;
          } while (!newOption && index < optionsArray.length - 1 && index > 0);

          newOption && newOption.focus();
          opts.focusedNode = newOption;
        }

        function focusNextOption() {
          focusOption('next');
        }

        function focusPrevOption() {
          focusOption('prev');
        }

        function checkCloseMenu(ev) {
          if (ev && ( ev.type == 'click') && (ev.currentTarget != dropDown[0])) return;
          if ( mouseOnScrollbar() ) return;

          var option = $mdUtil.getClosest(ev.target, 'md-option');
          if (option && option.hasAttribute && !option.hasAttribute('disabled')) {
            ev.preventDefault();
            ev.stopPropagation();
            if (!selectCtrl.isMultiple) {
              opts.restoreFocus = true;

              $mdUtil.nextTick(function () {
                $mdSelect.hide(selectCtrl.ngModel.$viewValue);
              }, true);
            }
          }
          /**
           * check if the mouseup event was on a scrollbar
           */
          function mouseOnScrollbar() {
            var clickOnScrollbar = false;
            if (ev && (ev.currentTarget.children.length > 0)) {
              var child = ev.currentTarget.children[0];
              var hasScrollbar = child.scrollHeight > child.clientHeight;
              if (hasScrollbar && child.children.length > 0) {
                var relPosX = ev.pageX - ev.currentTarget.getBoundingClientRect().left;
                if (relPosX > child.querySelector('md-option').offsetWidth)
                  clickOnScrollbar = true;
              }
            }
            return clickOnScrollbar;
          }
        }
      }

    }

    /**
     * To notify listeners that the Select menu has closed,
     * trigger the [optional] user-defined expression
     */
    function announceClosed(opts) {
      var mdSelect = opts.selectCtrl;
      if (mdSelect) {
        var menuController = opts.selectEl.controller('mdSelectMenu');
        mdSelect.setLabelText(menuController ? menuController.selectedLabels() : '');
        mdSelect.triggerClose();
      }
    }


    /**
     * Calculate the
     */
    function calculateMenuPositions(scope, element, opts) {
      var
        containerNode = element[0],
        targetNode = opts.target[0].children[0], // target the label
        parentNode = $document[0].body,
        selectNode = opts.selectEl[0],
        contentNode = opts.contentEl[0],
        parentRect = parentNode.getBoundingClientRect(),
        targetRect = targetNode.getBoundingClientRect(),
        shouldOpenAroundTarget = false,
        bounds = {
          left: parentRect.left + SELECT_EDGE_MARGIN,
          top: SELECT_EDGE_MARGIN,
          bottom: parentRect.height - SELECT_EDGE_MARGIN,
          right: parentRect.width - SELECT_EDGE_MARGIN - ($mdUtil.floatingScrollbars() ? 16 : 0)
        },
        spaceAvailable = {
          top: targetRect.top - bounds.top,
          left: targetRect.left - bounds.left,
          right: bounds.right - (targetRect.left + targetRect.width),
          bottom: bounds.bottom - (targetRect.top + targetRect.height)
        },
        maxWidth = parentRect.width - SELECT_EDGE_MARGIN * 2,
        selectedNode = selectNode.querySelector('md-option[selected]'),
        optionNodes = selectNode.getElementsByTagName('md-option'),
        optgroupNodes = selectNode.getElementsByTagName('md-optgroup'),
        isScrollable = calculateScrollable(element, contentNode),
        centeredNode;

      var loading = isPromiseLike(opts.loadingAsync);
      if (!loading) {
        // If a selected node, center around that
        if (selectedNode) {
          centeredNode = selectedNode;
          // If there are option groups, center around the first option group
        } else if (optgroupNodes.length) {
          centeredNode = optgroupNodes[0];
          // Otherwise - if we are not loading async - center around the first optionNode
        } else if (optionNodes.length) {
          centeredNode = optionNodes[0];
          // In case there are no options, center on whatever's in there... (eg progress indicator)
        } else {
          centeredNode = contentNode.firstElementChild || contentNode;
        }
      } else {
        // If loading, center on progress indicator
        centeredNode = contentNode.firstElementChild || contentNode;
      }

      if (contentNode.offsetWidth > maxWidth) {
        contentNode.style['max-width'] = maxWidth + 'px';
      } else {
        contentNode.style.maxWidth = null;
      }
      if (shouldOpenAroundTarget) {
        contentNode.style['min-width'] = targetRect.width + 'px';
      }

      // Remove padding before we compute the position of the menu
      if (isScrollable) {
        selectNode.classList.add('md-overflow');
      }

      var focusedNode = centeredNode;
      if ((focusedNode.tagName || '').toUpperCase() === 'MD-OPTGROUP') {
        focusedNode = optionNodes[0] || contentNode.firstElementChild || contentNode;
        centeredNode = focusedNode;
      }
      // Cache for autoFocus()
      opts.focusedNode = focusedNode;

      // Get the selectMenuRect *after* max-width is possibly set above
      containerNode.style.display = 'block';
      var selectMenuRect = selectNode.getBoundingClientRect();
      var centeredRect = getOffsetRect(centeredNode);

      if (centeredNode) {
        var centeredStyle = $window.getComputedStyle(centeredNode);
        centeredRect.paddingLeft = parseInt(centeredStyle.paddingLeft, 10) || 0;
        centeredRect.paddingRight = parseInt(centeredStyle.paddingRight, 10) || 0;
      }

      if (isScrollable) {
        var scrollBuffer = contentNode.offsetHeight / 2;
        contentNode.scrollTop = centeredRect.top + centeredRect.height / 2 - scrollBuffer;

        if (spaceAvailable.top < scrollBuffer) {
          contentNode.scrollTop = Math.min(
            centeredRect.top,
            contentNode.scrollTop + scrollBuffer - spaceAvailable.top
          );
        } else if (spaceAvailable.bottom < scrollBuffer) {
          contentNode.scrollTop = Math.max(
            centeredRect.top + centeredRect.height - selectMenuRect.height,
            contentNode.scrollTop - scrollBuffer + spaceAvailable.bottom
          );
        }
      }

      var left, top, transformOrigin, minWidth, fontSize;
      if (shouldOpenAroundTarget) {
        left = targetRect.left;
        top = targetRect.top + targetRect.height;
        transformOrigin = '50% 0';
        if (top + selectMenuRect.height > bounds.bottom) {
          top = targetRect.top - selectMenuRect.height;
          transformOrigin = '50% 100%';
        }
      } else {
        left = (targetRect.left + centeredRect.left - centeredRect.paddingLeft) + 2;
        top = Math.floor(targetRect.top + targetRect.height / 2 - centeredRect.height / 2 -
            centeredRect.top + contentNode.scrollTop) + 2;

        transformOrigin = (centeredRect.left + targetRect.width / 2) + 'px ' +
          (centeredRect.top + centeredRect.height / 2 - contentNode.scrollTop) + 'px 0px';

        minWidth = Math.min(targetRect.width + centeredRect.paddingLeft + centeredRect.paddingRight, maxWidth);

        fontSize = window.getComputedStyle(targetNode)['font-size'];
      }

      // Keep left and top within the window
      var containerRect = containerNode.getBoundingClientRect();
      var scaleX = Math.round(100 * Math.min(targetRect.width / selectMenuRect.width, 1.0)) / 100;
      var scaleY = Math.round(100 * Math.min(targetRect.height / selectMenuRect.height, 1.0)) / 100;

      return {
        container: {
          element: angular.element(containerNode),
          styles: {
            left: Math.floor(clamp(bounds.left, left, bounds.right - containerRect.width)),
            top: Math.floor(clamp(bounds.top, top, bounds.bottom - containerRect.height)),
            'min-width': minWidth,
            'font-size': fontSize
          }
        },
        dropDown: {
          element: angular.element(selectNode),
          styles: {
            transformOrigin: transformOrigin,
            transform: !opts.alreadyOpen ? $mdUtil.supplant('scale({0},{1})', [scaleX, scaleY]) : ""
          }
        }
      };

    }

  }

  function isPromiseLike(obj) {
    return obj && angular.isFunction(obj.then);
  }

  function clamp(min, n, max) {
    return Math.max(min, Math.min(n, max));
  }

  function getOffsetRect(node) {
    return node ? {
      left: node.offsetLeft,
      top: node.offsetTop,
      width: node.offsetWidth,
      height: node.offsetHeight
    } : {left: 0, top: 0, width: 0, height: 0};
  }

  function calculateScrollable(element, contentNode) {
    var isScrollable = false;

    try {
      var oldDisplay = element[0].style.display;

      // Set the element's display to block so that this calculation is correct
      element[0].style.display = 'block';

      isScrollable = contentNode.scrollHeight > contentNode.offsetHeight;

      // Reset it back afterwards
      element[0].style.display = oldDisplay;
    } finally {
      // Nothing to do
    }
    return isScrollable;
  }

}

function shouldHandleKey(ev, $mdConstant) {
  var char = String.fromCharCode(ev.keyCode);
  var isNonUsefulKey = (ev.keyCode <= 31);

  return (char && char.length && !isNonUsefulKey &&
    !$mdConstant.isMetaKey(ev) && !$mdConstant.isFnLockKey(ev) && !$mdConstant.hasModifierKey(ev));
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.showHide
 */

// Add additional handlers to ng-show and ng-hide that notify directives
// contained within that they should recompute their size.
// These run in addition to AngularJS's built-in ng-hide and ng-show directives.
angular.module('material.components.showHide', [
  'material.core'
])
  .directive('ngShow', createDirective('ngShow', true))
  .directive('ngHide', createDirective('ngHide', false));


function createDirective(name, targetValue) {
  return ['$mdUtil', '$window', function($mdUtil, $window) {
    return {
      restrict: 'A',
      multiElement: true,
      link: function($scope, $element, $attr) {
        var unregister = $scope.$on('$md-resize-enable', function() {
          unregister();

          var node = $element[0];
          var cachedTransitionStyles = node.nodeType === $window.Node.ELEMENT_NODE ?
            $window.getComputedStyle(node) : {};

          $scope.$watch($attr[name], function(value) {
            if (!!value === targetValue) {
              $mdUtil.nextTick(function() {
                $scope.$broadcast('$md-resize');
              });

              var opts = {
                cachedTransitionStyles: cachedTransitionStyles
              };

              $mdUtil.dom.animator.waitTransitionEnd($element, opts).then(function() {
                $scope.$broadcast('$md-resize');
              });
            }
          });
        });
      }
    };
  }];
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.sidenav
 *
 * @description
 * A Sidenav QP component.
 */
SidenavService.$inject = ["$mdComponentRegistry", "$mdUtil", "$q", "$log"];
SidenavDirective.$inject = ["$mdMedia", "$mdUtil", "$mdConstant", "$mdTheming", "$mdInteraction", "$animate", "$compile", "$parse", "$log", "$q", "$document", "$window", "$$rAF"];
SidenavController.$inject = ["$scope", "$attrs", "$mdComponentRegistry", "$q", "$interpolate"];
angular
  .module('material.components.sidenav', [
    'material.core',
    'material.components.backdrop'
  ])
  .factory('$mdSidenav', SidenavService )
  .directive('mdSidenav', SidenavDirective)
  .directive('mdSidenavFocus', SidenavFocusDirective)
  .controller('$mdSidenavController', SidenavController);


/**
 * @ngdoc service
 * @name $mdSidenav
 * @module material.components.sidenav
 *
 * @description
 * `$mdSidenav` makes it easy to interact with multiple sidenavs
 * in an app. When looking up a sidenav instance, you can either look
 * it up synchronously or wait for it to be initializied asynchronously.
 * This is done by passing the second argument to `$mdSidenav`.
 *
 * @usage
 * <hljs lang="js">
 * // Async lookup for sidenav instance; will resolve when the instance is available
 * $mdSidenav(componentId, true).then(function(instance) {
 *   $log.debug( componentId + "is now ready" );
 * });
 * // Sync lookup for sidenav instance; this will resolve immediately.
 * $mdSidenav(componentId).then(function(instance) {
 *   $log.debug( componentId + "is now ready" );
 * });
 * // Async toggle the given sidenav;
 * // when instance is known ready and lazy lookup is not needed.
 * $mdSidenav(componentId)
 *    .toggle()
 *    .then(function(){
 *      $log.debug('toggled');
 *    });
 * // Async open the given sidenav
 * $mdSidenav(componentId)
 *    .open()
 *    .then(function(){
 *      $log.debug('opened');
 *    });
 * // Async close the given sidenav
 * $mdSidenav(componentId)
 *    .close()
 *    .then(function(){
 *      $log.debug('closed');
 *    });
 * // Sync check to see if the specified sidenav is set to be open
 * $mdSidenav(componentId).isOpen();
 * // Sync check to whether given sidenav is locked open
 * // If this is true, the sidenav will be open regardless of close()
 * $mdSidenav(componentId).isLockedOpen();
 * // On close callback to handle close, backdrop click or escape key pressed
 * // Callback happens BEFORE the close action occurs.
 * $mdSidenav(componentId).onClose(function () {
 *   $log.debug('closing');
 * });
 * </hljs>
 */
function SidenavService($mdComponentRegistry, $mdUtil, $q, $log) {
  var errorMsg = "SideNav '{0}' is not available! Did you use md-component-id='{0}'?";
  var service = {
        find    : findInstance,     //  sync  - returns proxy API
        waitFor : waitForInstance   //  async - returns promise
      };

  /**
   * Service API that supports three (3) usages:
   *   $mdSidenav().find("left")                       // sync (must already exist) or returns undefined
   *   $mdSidenav("left").toggle();                    // sync (must already exist) or returns reject promise;
   *   $mdSidenav("left",true).then( function(left){   // async returns instance when available
   *    left.toggle();
   *   });
   */
  return function(handle, enableWait) {
    if ( angular.isUndefined(handle) ) return service;

    var shouldWait = enableWait === true;
    var instance = service.find(handle, shouldWait);
    return  !instance && shouldWait ? service.waitFor(handle) :
            !instance && angular.isUndefined(enableWait) ? addLegacyAPI(service, handle) : instance;
  };

  /**
   * For failed instance/handle lookups, older-clients expect an response object with noops
   * that include `rejected promise APIs`
   */
  function addLegacyAPI(service, handle) {
      var falseFn  = function() { return false; };
      var rejectFn = function() {
            return $q.when($mdUtil.supplant(errorMsg, [handle || ""]));
          };

      return angular.extend({
        isLockedOpen : falseFn,
        isOpen       : falseFn,
        toggle       : rejectFn,
        open         : rejectFn,
        close        : rejectFn,
        onClose      : angular.noop,
        then : function(callback) {
          return waitForInstance(handle)
            .then(callback || angular.noop);
        }
       }, service);
    }
    /**
     * Synchronously lookup the controller instance for the specified sidNav instance which has been
     * registered with the markup `md-component-id`
     */
    function findInstance(handle, shouldWait) {
      var instance = $mdComponentRegistry.get(handle);

      if (!instance && !shouldWait) {

        // Report missing instance
        $log.error( $mdUtil.supplant(errorMsg, [handle || ""]) );

        // The component has not registered itself... most like NOT yet created
        // return null to indicate that the Sidenav is not in the DOM
        return undefined;
      }
      return instance;
    }

    /**
     * Asynchronously wait for the component instantiation,
     * Deferred lookup of component instance using $component registry
     */
    function waitForInstance(handle) {
      return $mdComponentRegistry.when(handle).catch($log.error);
    }
}
/**
 * @ngdoc directive
 * @name mdSidenavFocus
 * @module material.components.sidenav
 *
 * @restrict A
 *
 * @description
 * `mdSidenavFocus` provides a way to specify the focused element when a sidenav opens.
 * This is completely optional, as the sidenav itself is focused by default.
 *
 * @usage
 * <hljs lang="html">
 * <md-sidenav>
 *   <form>
 *     <md-input-container>
 *       <label for="testInput">Label</label>
 *       <input id="testInput" type="text" md-sidenav-focus>
 *     </md-input-container>
 *   </form>
 * </md-sidenav>
 * </hljs>
 **/
function SidenavFocusDirective() {
  return {
    restrict: 'A',
    require: '^mdSidenav',
    link: function(scope, element, attr, sidenavCtrl) {
      // @see $mdUtil.findFocusTarget(...)
    }
  };
}
/**
 * @ngdoc directive
 * @name mdSidenav
 * @module material.components.sidenav
 * @restrict E
 *
 * @description
 *
 * A Sidenav component that can be opened and closed programatically.
 *
 * By default, upon opening it will slide out on top of the main content area.
 *
 * For keyboard and screen reader accessibility, focus is sent to the sidenav wrapper by default.
 * It can be overridden with the `md-autofocus` directive on the child element you want focused.
 *
 * @usage
 * <hljs lang="html">
 * <div layout="row" ng-controller="MyController">
 *   <md-sidenav md-component-id="left" class="md-sidenav-left">
 *     Left Nav!
 *   </md-sidenav>
 *
 *   <md-content>
 *     Center Content
 *     <md-button ng-click="openLeftMenu()">
 *       Open Left Menu
 *     </md-button>
 *   </md-content>
 *
 *   <md-sidenav md-component-id="right"
 *     md-is-locked-open="$mdMedia('min-width: 333px')"
 *     class="md-sidenav-right">
 *     <form>
 *       <md-input-container>
 *         <label for="testInput">Test input</label>
 *         <input id="testInput" type="text"
 *                ng-model="data" md-autofocus>
 *       </md-input-container>
 *     </form>
 *   </md-sidenav>
 * </div>
 * </hljs>
 *
 * <hljs lang="js">
 * var app = angular.module('myApp', ['ngMaterial']);
 * app.controller('MyController', function($scope, $mdSidenav) {
 *   $scope.openLeftMenu = function() {
 *     $mdSidenav('left').toggle();
 *   };
 * });
 * </hljs>
 *
 * @param {expression=} md-is-open A model bound to whether the sidenav is opened.
 * @param {boolean=} md-disable-backdrop When present in the markup, the sidenav will not show a backdrop.
 * @param {string=} md-component-id componentId to use with $mdSidenav service.
 * @param {expression=} md-is-locked-open When this expression evaluates to true,
 * the sidenav 'locks open': it falls into the content's flow instead
 * of appearing over it. This overrides the `md-is-open` attribute.
 * @param {string=} md-disable-scroll-target Selector, pointing to an element, whose scrolling will
 * be disabled when the sidenav is opened. By default this is the sidenav's direct parent.
 *
* The $mdMedia() service is exposed to the is-locked-open attribute, which
 * can be given a media query or one of the `sm`, `gt-sm`, `md`, `gt-md`, `lg` or `gt-lg` presets.
 * Examples:
 *
 *   - `<md-sidenav md-is-locked-open="shouldLockOpen"></md-sidenav>`
 *   - `<md-sidenav md-is-locked-open="$mdMedia('min-width: 1000px')"></md-sidenav>`
 *   - `<md-sidenav md-is-locked-open="$mdMedia('sm')"></md-sidenav>` (locks open on small screens)
 */
function SidenavDirective($mdMedia, $mdUtil, $mdConstant, $mdTheming, $mdInteraction, $animate,
                          $compile, $parse, $log, $q, $document, $window, $$rAF) {
  return {
    restrict: 'E',
    scope: {
      isOpen: '=?mdIsOpen'
    },
    controller: '$mdSidenavController',
    compile: function(element) {
      element.addClass('md-closed').attr('tabIndex', '-1');
      return postLink;
    }
  };

  /**
   * Directive Post Link function...
   */
  function postLink(scope, element, attr, sidenavCtrl) {
    var lastParentOverFlow;
    var backdrop;
    var disableScrollTarget = null;
    var triggeringInteractionType;
    var triggeringElement = null;
    var previousContainerStyles;
    var promise = $q.when(true);
    var isLockedOpenParsed = $parse(attr.mdIsLockedOpen);
    var ngWindow = angular.element($window);
    var isLocked = function() {
      return isLockedOpenParsed(scope.$parent, {
        $media: function(arg) {
          $log.warn("$media is deprecated for is-locked-open. Use $mdMedia instead.");
          return $mdMedia(arg);
        },
        $mdMedia: $mdMedia
      });
    };

    if (attr.mdDisableScrollTarget) {
      disableScrollTarget = $document[0].querySelector(attr.mdDisableScrollTarget);

      if (disableScrollTarget) {
        disableScrollTarget = angular.element(disableScrollTarget);
      } else {
        $log.warn($mdUtil.supplant('mdSidenav: couldn\'t find element matching ' +
          'selector "{selector}". Falling back to parent.', { selector: attr.mdDisableScrollTarget }));
      }
    }

    if (!disableScrollTarget) {
      disableScrollTarget = element.parent();
    }

    // Only create the backdrop if the backdrop isn't disabled.
    if (!attr.hasOwnProperty('mdDisableBackdrop')) {
      backdrop = $mdUtil.createBackdrop(scope, "md-sidenav-backdrop md-opaque ng-enter");
    }

    element.addClass('_md');     // private md component indicator for styling
    $mdTheming(element);

    // The backdrop should inherit the sidenavs theme,
    // because the backdrop will take its parent theme by default.
    if ( backdrop ) $mdTheming.inherit(backdrop, element);

    element.on('$destroy', function() {
      backdrop && backdrop.remove();
      sidenavCtrl.destroy();
    });

    scope.$on('$destroy', function(){
      backdrop && backdrop.remove();
    });

    scope.$watch(isLocked, updateIsLocked);
    scope.$watch('isOpen', updateIsOpen);


    // Publish special accessor for the Controller instance
    sidenavCtrl.$toggleOpen = toggleOpen;

    /**
     * Toggle the DOM classes to indicate `locked`
     * @param isLocked
     */
    function updateIsLocked(isLocked, oldValue) {
      scope.isLockedOpen = isLocked;
      if (isLocked === oldValue) {
        element.toggleClass('md-locked-open', !!isLocked);
      } else {
        $animate[isLocked ? 'addClass' : 'removeClass'](element, 'md-locked-open');
      }
      if (backdrop) {
        backdrop.toggleClass('md-locked-open', !!isLocked);
      }
    }

    /**
     * Toggle the SideNav view and attach/detach listeners
     * @param isOpen
     */
    function updateIsOpen(isOpen) {
      // Support deprecated md-sidenav-focus attribute as fallback
      var focusEl = $mdUtil.findFocusTarget(element) || $mdUtil.findFocusTarget(element,'[md-sidenav-focus]') || element;
      var parent = element.parent();

      parent[isOpen ? 'on' : 'off']('keydown', onKeyDown);
      if (backdrop) backdrop[isOpen ? 'on' : 'off']('click', close);

      var restorePositioning = updateContainerPositions(parent, isOpen);

      if ( isOpen ) {
        // Capture upon opening..
        triggeringElement = $document[0].activeElement;
        triggeringInteractionType = $mdInteraction.getLastInteractionType();
      }

      disableParentScroll(isOpen);

      return promise = $q.all([
        isOpen && backdrop ? $animate.enter(backdrop, parent) : backdrop ?
                             $animate.leave(backdrop) : $q.when(true),
        $animate[isOpen ? 'removeClass' : 'addClass'](element, 'md-closed')
      ]).then(function() {
        // Perform focus when animations are ALL done...
        if (scope.isOpen) {
          $$rAF(function() {
            // Notifies child components that the sidenav was opened. Should wait
            // a frame in order to allow for the element height to be computed.
            ngWindow.triggerHandler('resize');
          });

          focusEl && focusEl.focus();
        }

        // Restores the positioning on the sidenav and backdrop.
        restorePositioning && restorePositioning();
      });
    }

    function updateContainerPositions(parent, willOpen) {
      var drawerEl = element[0];
      var scrollTop = parent[0].scrollTop;

      if (willOpen && scrollTop) {
        previousContainerStyles = {
          top: drawerEl.style.top,
          bottom: drawerEl.style.bottom,
          height: drawerEl.style.height
        };

        // When the parent is scrolled down, then we want to be able to show the sidenav at the current scroll
        // position. We're moving the sidenav down to the correct scroll position and apply the height of the
        // parent, to increase the performance. Using 100% as height, will impact the performance heavily.
        var positionStyle = {
          top: scrollTop + 'px',
          bottom: 'auto',
          height: parent[0].clientHeight + 'px'
        };

        // Apply the new position styles to the sidenav and backdrop.
        element.css(positionStyle);
        backdrop.css(positionStyle);
      }

      // When the sidenav is closing and we have previous defined container styles,
      // then we return a restore function, which resets the sidenav and backdrop.
      if (!willOpen && previousContainerStyles) {
        return function() {
          drawerEl.style.top = previousContainerStyles.top;
          drawerEl.style.bottom = previousContainerStyles.bottom;
          drawerEl.style.height = previousContainerStyles.height;

          backdrop[0].style.top = null;
          backdrop[0].style.bottom = null;
          backdrop[0].style.height = null;

          previousContainerStyles = null;
        };
      }
    }

    /**
     * Prevent parent scrolling (when the SideNav is open)
     */
    function disableParentScroll(disabled) {
      if ( disabled && !lastParentOverFlow ) {
        lastParentOverFlow = disableScrollTarget.css('overflow');
        disableScrollTarget.css('overflow', 'hidden');
      } else if (angular.isDefined(lastParentOverFlow)) {
        disableScrollTarget.css('overflow', lastParentOverFlow);
        lastParentOverFlow = undefined;
      }
    }

    /**
     * Toggle the sideNav view and publish a promise to be resolved when
     * the view animation finishes.
     *
     * @param isOpen
     * @returns {*}
     */
    function toggleOpen( isOpen ) {
      if (scope.isOpen == isOpen ) {

        return $q.when(true);

      } else {
        if (scope.isOpen && sidenavCtrl.onCloseCb) sidenavCtrl.onCloseCb();

        return $q(function(resolve){
          // Toggle value to force an async `updateIsOpen()` to run
          scope.isOpen = isOpen;

          $mdUtil.nextTick(function() {
            // When the current `updateIsOpen()` animation finishes
            promise.then(function(result) {

              if ( !scope.isOpen && triggeringElement && triggeringInteractionType === 'keyboard') {
                // reset focus to originating element (if available) upon close
                triggeringElement.focus();
                triggeringElement = null;
              }

              resolve(result);
            });
          });

        });

      }
    }

    /**
     * Auto-close sideNav when the `escape` key is pressed.
     * @param evt
     */
    function onKeyDown(ev) {
      var isEscape = (ev.keyCode === $mdConstant.KEY_CODE.ESCAPE);
      return isEscape ? close(ev) : $q.when(true);
    }

    /**
     * With backdrop `clicks` or `escape` key-press, immediately
     * apply the CSS close transition... Then notify the controller
     * to close() and perform its own actions.
     */
    function close(ev) {
      ev.preventDefault();

      return sidenavCtrl.close();
    }

  }
}

/*
 * @private
 * @ngdoc controller
 * @name SidenavController
 * @module material.components.sidenav
 */
function SidenavController($scope, $attrs, $mdComponentRegistry, $q, $interpolate) {

  var self = this;

  // Use Default internal method until overridden by directive postLink

  // Synchronous getters
  self.isOpen = function() { return !!$scope.isOpen; };
  self.isLockedOpen = function() { return !!$scope.isLockedOpen; };

  // Synchronous setters
  self.onClose = function (callback) {
    self.onCloseCb = callback;
    return self;
  };

  // Async actions
  self.open   = function() { return self.$toggleOpen( true );  };
  self.close  = function() { return self.$toggleOpen( false ); };
  self.toggle = function() { return self.$toggleOpen( !$scope.isOpen );  };
  self.$toggleOpen = function(value) { return $q.when($scope.isOpen = value); };

  // Evaluate the component id.
  var rawId = $attrs.mdComponentId;
  var hasDataBinding = rawId && rawId.indexOf($interpolate.startSymbol()) > -1;
  var componentId = hasDataBinding ? $interpolate(rawId)($scope.$parent) : rawId;

  // Register the component.
  self.destroy = $mdComponentRegistry.register(self, componentId);

  // Watch and update the component, if the id has changed.
  if (hasDataBinding) {
    $attrs.$observe('mdComponentId', function(id) {
      if (id && id !== self.$$mdHandle) {
        self.destroy(); // `destroy` only deregisters the old component id so we can add the new one.
        self.destroy = $mdComponentRegistry.register(self, id);
      }
    });
  }
}

})();
(function(){
"use strict";

  /**
   * @ngdoc module
   * @name material.components.slider
   */
SliderDirective.$inject = ["$$rAF", "$window", "$mdAria", "$mdUtil", "$mdConstant", "$mdTheming", "$mdGesture", "$parse", "$log", "$timeout"];
  angular.module('material.components.slider', [
    'material.core'
  ])
  .directive('mdSlider', SliderDirective)
  .directive('mdSliderContainer', SliderContainerDirective);

/**
 * @ngdoc directive
 * @name mdSliderContainer
 * @module material.components.slider
 * @restrict E
 * @description
 * The `<md-slider-container>` contains slider with two other elements.
 *
 *
 * @usage
 * <h4>Normal Mode</h4>
 * <hljs lang="html">
 * </hljs>
 */
function SliderContainerDirective() {
  return {
    controller: function () {},
    compile: function (elem) {
      var slider = elem.find('md-slider');

      if (!slider) {
        return;
      }

      var vertical = slider.attr('md-vertical');

      if (vertical !== undefined) {
        elem.attr('md-vertical', '');
      }

      if(!slider.attr('flex')) {
        slider.attr('flex', '');
      }

      return function postLink(scope, element, attr, ctrl) {
        element.addClass('_md');     // private md component indicator for styling

        // We have to manually stop the $watch on ngDisabled because it exists
        // on the parent scope, and won't be automatically destroyed when
        // the component is destroyed.
        function setDisable(value) {
          element.children().attr('disabled', value);
          element.find('input').attr('disabled', value);
        }

        var stopDisabledWatch = angular.noop;

        if (attr.disabled) {
          setDisable(true);
        }
        else if (attr.ngDisabled) {
          stopDisabledWatch = scope.$watch(attr.ngDisabled, function (value) {
            setDisable(value);
          });
        }

        scope.$on('$destroy', function () {
          stopDisabledWatch();
        });

        var initialMaxWidth;

        ctrl.fitInputWidthToTextLength = function (length) {
          var input = element[0].querySelector('md-input-container');

          if (input) {
            var computedStyle = getComputedStyle(input);
            var minWidth = parseInt(computedStyle.minWidth);
            var padding = parseInt(computedStyle.padding) * 2;

            initialMaxWidth = initialMaxWidth || parseInt(computedStyle.maxWidth);
            var newMaxWidth = Math.max(initialMaxWidth, minWidth + padding + (minWidth / 2 * length));

            input.style.maxWidth = newMaxWidth + 'px';
          }
        };
      };
    }
  };
}

/**
 * @ngdoc directive
 * @name mdSlider
 * @module material.components.slider
 * @restrict E
 * @description
 * The `<md-slider>` component allows the user to choose from a range of
 * values.
 *
 * As per the [material design spec](http://www.google.com/design/spec/style/color.html#color-ui-color-application)
 * the slider is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * It has two modes: 'normal' mode, where the user slides between a wide range
 * of values, and 'discrete' mode, where the user slides between only a few
 * select values.
 *
 * To enable discrete mode, add the `md-discrete` attribute to a slider,
 * and use the `step` attribute to change the distance between
 * values the user is allowed to pick.
 *
 * @usage
 * <h4>Normal Mode</h4>
 * <hljs lang="html">
 * <md-slider ng-model="myValue" min="5" max="500">
 * </md-slider>
 * </hljs>
 * <h4>Discrete Mode</h4>
 * <hljs lang="html">
 * <md-slider md-discrete ng-model="myDiscreteValue" step="10" min="10" max="130">
 * </md-slider>
 * </hljs>
 * <h4>Invert Mode</h4>
 * <hljs lang="html">
 * <md-slider md-invert ng-model="myValue" step="10" min="10" max="130">
 * </md-slider>
 * </hljs>
 *
 * @param {boolean=} md-discrete Whether to enable discrete mode.
 * @param {boolean=} md-invert Whether to enable invert mode.
 * @param {number=} step The distance between values the user is allowed to pick. Default 1.
 * @param {number=} min The minimum value the user is allowed to pick. Default 0.
 * @param {number=} max The maximum value the user is allowed to pick. Default 100.
 * @param {number=} round The amount of numbers after the decimal point, maximum is 6 to prevent scientific notation. Default 3.
 */
function SliderDirective($$rAF, $window, $mdAria, $mdUtil, $mdConstant, $mdTheming, $mdGesture, $parse, $log, $timeout) {
  return {
    scope: {},
    require: ['?ngModel', '?^mdSliderContainer'],
    template:
      '<div class="md-slider-wrapper">' +
        '<div class="md-slider-content">' +
          '<div class="md-track-container">' +
            '<div class="md-track"></div>' +
            '<div class="md-track md-track-fill"></div>' +
            '<div class="md-track-ticks"></div>' +
          '</div>' +
          '<div class="md-thumb-container">' +
            '<div class="md-thumb"></div>' +
            '<div class="md-focus-thumb"></div>' +
            '<div class="md-focus-ring"></div>' +
            '<div class="md-sign">' +
              '<span class="md-thumb-text"></span>' +
            '</div>' +
            '<div class="md-disabled-thumb"></div>' +
          '</div>' +
        '</div>' +
      '</div>',
    compile: compile
  };

  // **********************************************************
  // Private Methods
  // **********************************************************

  function compile (tElement, tAttrs) {
    var wrapper = angular.element(tElement[0].getElementsByClassName('md-slider-wrapper'));

    var tabIndex = tAttrs.tabindex || 0;
    wrapper.attr('tabindex', tabIndex);

    if (tAttrs.disabled || tAttrs.ngDisabled) wrapper.attr('tabindex', -1);

    wrapper.attr('role', 'slider');

    $mdAria.expect(tElement, 'aria-label');

    return postLink;
  }

  function postLink(scope, element, attr, ctrls) {
    $mdTheming(element);
    var ngModelCtrl = ctrls[0] || {
      // Mock ngModelController if it doesn't exist to give us
      // the minimum functionality needed
      $setViewValue: function(val) {
        this.$viewValue = val;
        this.$viewChangeListeners.forEach(function(cb) { cb(); });
      },
      $parsers: [],
      $formatters: [],
      $viewChangeListeners: []
    };

    var containerCtrl = ctrls[1];
    var container = angular.element($mdUtil.getClosest(element, '_md-slider-container', true));
    var isDisabled = attr.ngDisabled ? angular.bind(null, $parse(attr.ngDisabled), scope.$parent) : function () {
          return element[0].hasAttribute('disabled');
        };

    var thumb = angular.element(element[0].querySelector('.md-thumb'));
    var thumbText = angular.element(element[0].querySelector('.md-thumb-text'));
    var thumbContainer = thumb.parent();
    var trackContainer = angular.element(element[0].querySelector('.md-track-container'));
    var activeTrack = angular.element(element[0].querySelector('.md-track-fill'));
    var tickContainer = angular.element(element[0].querySelector('.md-track-ticks'));
    var wrapper = angular.element(element[0].getElementsByClassName('md-slider-wrapper'));
    var content = angular.element(element[0].getElementsByClassName('md-slider-content'));
    var throttledRefreshDimensions = $mdUtil.throttle(refreshSliderDimensions, 5000);

    // Default values, overridable by attrs
    var DEFAULT_ROUND = 3;
    var vertical = angular.isDefined(attr.mdVertical);
    var discrete = angular.isDefined(attr.mdDiscrete);
    var invert = angular.isDefined(attr.mdInvert);
    angular.isDefined(attr.min) ? attr.$observe('min', updateMin) : updateMin(0);
    angular.isDefined(attr.max) ? attr.$observe('max', updateMax) : updateMax(100);
    angular.isDefined(attr.step)? attr.$observe('step', updateStep) : updateStep(1);
    angular.isDefined(attr.round)? attr.$observe('round', updateRound) : updateRound(DEFAULT_ROUND);

    // We have to manually stop the $watch on ngDisabled because it exists
    // on the parent scope, and won't be automatically destroyed when
    // the component is destroyed.
    var stopDisabledWatch = angular.noop;
    if (attr.ngDisabled) {
      stopDisabledWatch = scope.$parent.$watch(attr.ngDisabled, updateAriaDisabled);
    }

    $mdGesture.register(wrapper, 'drag', { horizontal: !vertical });

    scope.mouseActive = false;

    wrapper
      .on('keydown', keydownListener)
      .on('mousedown', mouseDownListener)
      .on('focus', focusListener)
      .on('blur', blurListener)
      .on('$md.pressdown', onPressDown)
      .on('$md.pressup', onPressUp)
      .on('$md.dragstart', onDragStart)
      .on('$md.drag', onDrag)
      .on('$md.dragend', onDragEnd);

    // On resize, recalculate the slider's dimensions and re-render
    function updateAll() {
      refreshSliderDimensions();
      ngModelRender();
    }
    setTimeout(updateAll, 0);

    var debouncedUpdateAll = $$rAF.throttle(updateAll);
    angular.element($window).on('resize', debouncedUpdateAll);

    scope.$on('$destroy', function() {
      angular.element($window).off('resize', debouncedUpdateAll);
    });

    ngModelCtrl.$render = ngModelRender;
    ngModelCtrl.$viewChangeListeners.push(ngModelRender);
    ngModelCtrl.$formatters.push(minMaxValidator);
    ngModelCtrl.$formatters.push(stepValidator);

    /**
     * Attributes
     */
    var min;
    var max;
    var step;
    var round;
    function updateMin(value) {
      min = parseFloat(value);
      element.attr('aria-valuemin', value);
      updateAll();
    }
    function updateMax(value) {
      max = parseFloat(value);
      element.attr('aria-valuemax', value);
      updateAll();
    }
    function updateStep(value) {
      step = parseFloat(value);
    }
    function updateRound(value) {
      // Set max round digits to 6, after 6 the input uses scientific notation
      round = minMaxValidator(parseInt(value), 0, 6);
    }
    function updateAriaDisabled() {
      element.attr('aria-disabled', !!isDisabled());
    }

    // Draw the ticks with canvas.
    // The alternative to drawing ticks with canvas is to draw one element for each tick,
    // which could quickly become a performance bottleneck.
    var tickCanvas, tickCtx;
    function redrawTicks() {
      if (!discrete || isDisabled()) return;
      if ( angular.isUndefined(step) )         return;

      if ( step <= 0 ) {
        var msg = 'Slider step value must be greater than zero when in discrete mode';
        $log.error(msg);
        throw new Error(msg);
      }

      var numSteps = Math.floor( (max - min) / step );
      if (!tickCanvas) {
        tickCanvas = angular.element('<canvas>').css('position', 'absolute');
        tickContainer.append(tickCanvas);

        tickCtx = tickCanvas[0].getContext('2d');
      }

      var dimensions = getSliderDimensions();

      // If `dimensions` doesn't have height and width it might be the first attempt so we will refresh dimensions
      if (dimensions && !dimensions.height && !dimensions.width) {
        refreshSliderDimensions();
        dimensions = sliderDimensions;
      }

      tickCanvas[0].width = dimensions.width;
      tickCanvas[0].height = dimensions.height;

      var distance;
      for (var i = 0; i <= numSteps; i++) {
        var trackTicksStyle = $window.getComputedStyle(tickContainer[0]);
        tickCtx.fillStyle = trackTicksStyle.color || 'black';

        distance = Math.floor((vertical ? dimensions.height : dimensions.width) * (i / numSteps));

        tickCtx.fillRect(vertical ? 0 : distance - 1,
          vertical ? distance - 1 : 0,
          vertical ? dimensions.width : 2,
          vertical ? 2 : dimensions.height);
      }
    }

    function clearTicks() {
      if(tickCanvas && tickCtx) {
        var dimensions = getSliderDimensions();
        tickCtx.clearRect(0, 0, dimensions.width, dimensions.height);
      }
    }

    /**
     * Refreshing Dimensions
     */
    var sliderDimensions = {};
    refreshSliderDimensions();
    function refreshSliderDimensions() {
      sliderDimensions = trackContainer[0].getBoundingClientRect();
    }
    function getSliderDimensions() {
      throttledRefreshDimensions();
      return sliderDimensions;
    }

    /**
     * left/right/up/down arrow listener
     */
    function keydownListener(ev) {
      if (isDisabled()) return;

      var changeAmount;
      if (vertical ? ev.keyCode === $mdConstant.KEY_CODE.DOWN_ARROW : ev.keyCode === $mdConstant.KEY_CODE.LEFT_ARROW) {
        changeAmount = -step;
      } else if (vertical ? ev.keyCode === $mdConstant.KEY_CODE.UP_ARROW : ev.keyCode === $mdConstant.KEY_CODE.RIGHT_ARROW) {
        changeAmount = step;
      }
      changeAmount = invert ? -changeAmount : changeAmount;
      if (changeAmount) {
        if (ev.metaKey || ev.ctrlKey || ev.altKey) {
          changeAmount *= 4;
        }
        ev.preventDefault();
        ev.stopPropagation();
        scope.$evalAsync(function() {
          setModelValue(ngModelCtrl.$viewValue + changeAmount);
        });
      }
    }

    function mouseDownListener() {
      redrawTicks();

      scope.mouseActive = true;
      wrapper.removeClass('md-focused');

      $timeout(function() {
        scope.mouseActive = false;
      }, 100);
    }

    function focusListener() {
      if (scope.mouseActive === false) {
        wrapper.addClass('md-focused');
      }
    }

    function blurListener() {
      wrapper.removeClass('md-focused');
      element.removeClass('md-active');
      clearTicks();
    }

    /**
     * ngModel setters and validators
     */
    function setModelValue(value) {
      ngModelCtrl.$setViewValue( minMaxValidator(stepValidator(value)) );
    }
    function ngModelRender() {
      if (isNaN(ngModelCtrl.$viewValue)) {
        ngModelCtrl.$viewValue = ngModelCtrl.$modelValue;
      }

      ngModelCtrl.$viewValue = minMaxValidator(ngModelCtrl.$viewValue);

      var percent = valueToPercent(ngModelCtrl.$viewValue);
      scope.modelValue = ngModelCtrl.$viewValue;
      element.attr('aria-valuenow', ngModelCtrl.$viewValue);
      setSliderPercent(percent);
      thumbText.text( ngModelCtrl.$viewValue );
    }

    function minMaxValidator(value, minValue, maxValue) {
      if (angular.isNumber(value)) {
        minValue = angular.isNumber(minValue) ? minValue : min;
        maxValue = angular.isNumber(maxValue) ? maxValue : max;

        return Math.max(minValue, Math.min(maxValue, value));
      }
    }

    function stepValidator(value) {
      if (angular.isNumber(value)) {
        var formattedValue = (Math.round((value - min) / step) * step + min);
        formattedValue = (Math.round(formattedValue * Math.pow(10, round)) / Math.pow(10, round));

        if (containerCtrl && containerCtrl.fitInputWidthToTextLength){
          $mdUtil.debounce(function () {
            containerCtrl.fitInputWidthToTextLength(formattedValue.toString().length);
          }, 100)();
        }

        return formattedValue;
      }
    }

    /**
     * @param percent 0-1
     */
    function setSliderPercent(percent) {

      percent = clamp(percent);

      var thumbPosition = (percent * 100) + '%';
      var activeTrackPercent = invert ? (1 - percent) * 100 + '%' : thumbPosition;

      if (vertical) {
        thumbContainer.css('bottom', thumbPosition);
      }
      else {
        $mdUtil.bidiProperty(thumbContainer, 'left', 'right', thumbPosition);
      }

      
      activeTrack.css(vertical ? 'height' : 'width', activeTrackPercent);

      element.toggleClass((invert ? 'md-max' : 'md-min'), percent === 0);
      element.toggleClass((invert ? 'md-min' : 'md-max'), percent === 1);
    }

    /**
     * Slide listeners
     */
    var isDragging = false;

    function onPressDown(ev) {
      if (isDisabled()) return;

      element.addClass('md-active');
      element[0].focus();
      refreshSliderDimensions();

      var exactVal = percentToValue( positionToPercent( vertical ? ev.pointer.y : ev.pointer.x ));
      var closestVal = minMaxValidator( stepValidator(exactVal) );
      scope.$apply(function() {
        setModelValue( closestVal );
        setSliderPercent( valueToPercent(closestVal));
      });
    }
    function onPressUp(ev) {
      if (isDisabled()) return;

      element.removeClass('md-dragging');

      var exactVal = percentToValue( positionToPercent( vertical ? ev.pointer.y : ev.pointer.x ));
      var closestVal = minMaxValidator( stepValidator(exactVal) );
      scope.$apply(function() {
        setModelValue(closestVal);
        ngModelRender();
      });
    }
    function onDragStart(ev) {
      if (isDisabled()) return;
      isDragging = true;

      ev.stopPropagation();

      element.addClass('md-dragging');
      setSliderFromEvent(ev);
    }
    function onDrag(ev) {
      if (!isDragging) return;
      ev.stopPropagation();
      setSliderFromEvent(ev);
    }
    function onDragEnd(ev) {
      if (!isDragging) return;
      ev.stopPropagation();
      isDragging = false;
    }

    function setSliderFromEvent(ev) {
      // While panning discrete, update only the
      // visual positioning but not the model value.
      if ( discrete ) adjustThumbPosition( vertical ? ev.pointer.y : ev.pointer.x );
      else            doSlide( vertical ? ev.pointer.y : ev.pointer.x );
    }

    /**
     * Slide the UI by changing the model value
     * @param x
     */
    function doSlide( x ) {
      scope.$evalAsync( function() {
        setModelValue( percentToValue( positionToPercent(x) ));
      });
    }

    /**
     * Slide the UI without changing the model (while dragging/panning)
     * @param x
     */
    function adjustThumbPosition( x ) {
      var exactVal = percentToValue( positionToPercent( x ));
      var closestVal = minMaxValidator( stepValidator(exactVal) );
      setSliderPercent( positionToPercent(x) );
      thumbText.text( closestVal );
    }

    /**
    * Clamps the value to be between 0 and 1.
    * @param {number} value The value to clamp.
    * @returns {number}
    */
    function clamp(value) {
      return Math.max(0, Math.min(value || 0, 1));
    }

    /**
     * Convert position on slider to percentage value of offset from beginning...
     * @param position
     * @returns {number}
     */
    function positionToPercent( position ) {
      var offset = vertical ? sliderDimensions.top : sliderDimensions.left;
      var size = vertical ? sliderDimensions.height : sliderDimensions.width;
      var calc = (position - offset) / size;

      if (!vertical && $mdUtil.bidi() === 'rtl') {
        calc = 1 - calc;
      }

      return Math.max(0, Math.min(1, vertical ? 1 - calc : calc));
    }

    /**
     * Convert percentage offset on slide to equivalent model value
     * @param percent
     * @returns {*}
     */
    function percentToValue( percent ) {
      var adjustedPercent = invert ? (1 - percent) : percent;
      return (min + adjustedPercent * (max - min));
    }

    function valueToPercent( val ) {
      var percent = (val - min) / (max - min);
      return invert ? (1 - percent) : percent;
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.sticky
 * @description
 * Sticky effects for md
 *
 */
MdSticky.$inject = ["$mdConstant", "$$rAF", "$mdUtil", "$compile"];
angular
  .module('material.components.sticky', [
    'material.core',
    'material.components.content'
  ])
  .factory('$mdSticky', MdSticky);

/**
 * @ngdoc service
 * @name $mdSticky
 * @module material.components.sticky
 *
 * @description
 * The `$mdSticky`service provides a mixin to make elements sticky.
 *
 * Whenever the current browser supports stickiness natively, the `$mdSticky` service will just
 * use the native browser stickiness.
 *
 * By default the `$mdSticky` service compiles the cloned element, when not specified through the `elementClone`
 * parameter, in the same scope as the actual element lives.
 *
 *
 * <h3>Notes</h3>
 * When using an element which is containing a compiled directive, which changed its DOM structure during compilation,
 * you should compile the clone yourself using the plain template.<br/><br/>
 * See the right usage below:
 * <hljs lang="js">
 *   angular.module('myModule')
 *     .directive('stickySelect', function($mdSticky, $compile) {
 *       var SELECT_TEMPLATE =
 *         '<md-select ng-model="selected">' +
 *           '<md-option>Option 1</md-option>' +
 *         '</md-select>';
 *
 *       return {
 *         restrict: 'E',
 *         replace: true,
 *         template: SELECT_TEMPLATE,
 *         link: function(scope,element) {
 *           $mdSticky(scope, element, $compile(SELECT_TEMPLATE)(scope));
 *         }
 *       };
 *     });
 * </hljs>
 *
 * @usage
 * <hljs lang="js">
 *   angular.module('myModule')
 *     .directive('stickyText', function($mdSticky, $compile) {
 *       return {
 *         restrict: 'E',
 *         template: '<span>Sticky Text</span>',
 *         link: function(scope,element) {
 *           $mdSticky(scope, element);
 *         }
 *       };
 *     });
 * </hljs>
 *
 * @returns A `$mdSticky` function that takes three arguments:
 *   - `scope`
 *   - `element`: The element that will be 'sticky'
 *   - `elementClone`: A clone of the element, that will be shown
 *     when the user starts scrolling past the original element.
 *     If not provided, it will use the result of `element.clone()` and compiles it in the given scope.
 */
function MdSticky($mdConstant, $$rAF, $mdUtil, $compile) {

  var browserStickySupport = $mdUtil.checkStickySupport();

  /**
   * Registers an element as sticky, used internally by directives to register themselves
   */
  return function registerStickyElement(scope, element, stickyClone) {
    var contentCtrl = element.controller('mdContent');
    if (!contentCtrl) return;

    if (browserStickySupport) {
      element.css({
        position: browserStickySupport,
        top: 0,
        'z-index': 2
      });
    } else {
      var $$sticky = contentCtrl.$element.data('$$sticky');
      if (!$$sticky) {
        $$sticky = setupSticky(contentCtrl);
        contentCtrl.$element.data('$$sticky', $$sticky);
      }

      // Compile our cloned element, when cloned in this service, into the given scope.
      var cloneElement = stickyClone || $compile(element.clone())(scope);

      var deregister = $$sticky.add(element, cloneElement);
      scope.$on('$destroy', deregister);
    }
  };

  function setupSticky(contentCtrl) {
    var contentEl = contentCtrl.$element;

    // Refresh elements is very expensive, so we use the debounced
    // version when possible.
    var debouncedRefreshElements = $$rAF.throttle(refreshElements);

    // setupAugmentedScrollEvents gives us `$scrollstart` and `$scroll`,
    // more reliable than `scroll` on android.
    setupAugmentedScrollEvents(contentEl);
    contentEl.on('$scrollstart', debouncedRefreshElements);
    contentEl.on('$scroll', onScroll);

    var self;
    return self = {
      prev: null,
      current: null, //the currently stickied item
      next: null,
      items: [],
      add: add,
      refreshElements: refreshElements
    };

    /***************
     * Public
     ***************/
    // Add an element and its sticky clone to this content's sticky collection
    function add(element, stickyClone) {
      stickyClone.addClass('md-sticky-clone');

      var item = {
        element: element,
        clone: stickyClone
      };
      self.items.push(item);

      $mdUtil.nextTick(function() {
        contentEl.prepend(item.clone);
      });

      debouncedRefreshElements();

      return function remove() {
        self.items.forEach(function(item, index) {
          if (item.element[0] === element[0]) {
            self.items.splice(index, 1);
            item.clone.remove();
          }
        });
        debouncedRefreshElements();
      };
    }

    function refreshElements() {
      // Sort our collection of elements by their current position in the DOM.
      // We need to do this because our elements' order of being added may not
      // be the same as their order of display.
      self.items.forEach(refreshPosition);
      self.items = self.items.sort(function(a, b) {
        return a.top < b.top ? -1 : 1;
      });

      // Find which item in the list should be active, 
      // based upon the content's current scroll position
      var item;
      var currentScrollTop = contentEl.prop('scrollTop');
      for (var i = self.items.length - 1; i >= 0; i--) {
        if (currentScrollTop > self.items[i].top) {
          item = self.items[i];
          break;
        }
      }
      setCurrentItem(item);
    }

    /***************
     * Private
     ***************/

    // Find the `top` of an item relative to the content element,
    // and also the height.
    function refreshPosition(item) {
      // Find the top of an item by adding to the offsetHeight until we reach the 
      // content element.
      var current = item.element[0];
      item.top = 0;
      item.left = 0;
      item.right = 0;
      while (current && current !== contentEl[0]) {
        item.top += current.offsetTop;
        item.left += current.offsetLeft;
        if ( current.offsetParent ){
          item.right += current.offsetParent.offsetWidth - current.offsetWidth - current.offsetLeft; //Compute offsetRight
        }
        current = current.offsetParent;
      }
      item.height = item.element.prop('offsetHeight');

      var defaultVal = $mdUtil.floatingScrollbars() ? '0' : undefined;
      $mdUtil.bidi(item.clone, 'margin-left', item.left, defaultVal);
      $mdUtil.bidi(item.clone, 'margin-right', defaultVal, item.right);
    }

    // As we scroll, push in and select the correct sticky element.
    function onScroll() {
      var scrollTop = contentEl.prop('scrollTop');
      var isScrollingDown = scrollTop > (onScroll.prevScrollTop || 0);

      // Store the previous scroll so we know which direction we are scrolling
      onScroll.prevScrollTop = scrollTop;

      //
      // AT TOP (not scrolling)
      //
      if (scrollTop === 0) {
        // If we're at the top, just clear the current item and return
        setCurrentItem(null);
        return;
      }

      //
      // SCROLLING DOWN (going towards the next item)
      //
      if (isScrollingDown) {

        // If we've scrolled down past the next item's position, sticky it and return
        if (self.next && self.next.top <= scrollTop) {
          setCurrentItem(self.next);
          return;
        }

        // If the next item is close to the current one, push the current one up out of the way
        if (self.current && self.next && self.next.top - scrollTop <= self.next.height) {
          translate(self.current, scrollTop + (self.next.top - self.next.height - scrollTop));
          return;
        }
      }

      //
      // SCROLLING UP (not at the top & not scrolling down; must be scrolling up)
      //
      if (!isScrollingDown) {

        // If we've scrolled up past the previous item's position, sticky it and return
        if (self.current && self.prev && scrollTop < self.current.top) {
          setCurrentItem(self.prev);
          return;
        }

        // If the next item is close to the current one, pull the current one down into view
        if (self.next && self.current && (scrollTop >= (self.next.top - self.current.height))) {
          translate(self.current, scrollTop + (self.next.top - scrollTop - self.current.height));
          return;
        }
      }

      //
      // Otherwise, just move the current item to the proper place (scrolling up or down)
      //
      if (self.current) {
        translate(self.current, scrollTop);
      }
    }

    function setCurrentItem(item) {
      if (self.current === item) return;
      // Deactivate currently active item
      if (self.current) {
        translate(self.current, null);
        setStickyState(self.current, null);
      }

      // Activate new item if given
      if (item) {
        setStickyState(item, 'active');
      }

      self.current = item;
      var index = self.items.indexOf(item);
      // If index === -1, index + 1 = 0. It works out.
      self.next = self.items[index + 1];
      self.prev = self.items[index - 1];
      setStickyState(self.next, 'next');
      setStickyState(self.prev, 'prev');
    }

    function setStickyState(item, state) {
      if (!item || item.state === state) return;
      if (item.state) {
        item.clone.attr('sticky-prev-state', item.state);
        item.element.attr('sticky-prev-state', item.state);
      }
      item.clone.attr('sticky-state', state);
      item.element.attr('sticky-state', state);
      item.state = state;
    }

    function translate(item, amount) {
      if (!item) return;
      if (amount === null || amount === undefined) {
        if (item.translateY) {
          item.translateY = null;
          item.clone.css($mdConstant.CSS.TRANSFORM, '');
        }
      } else {
        item.translateY = amount;

        $mdUtil.bidi( item.clone, $mdConstant.CSS.TRANSFORM,
          'translate3d(' + item.left + 'px,' + amount + 'px,0)',
          'translateY(' + amount + 'px)'
        );
      }
    }
  }


  // Android 4.4 don't accurately give scroll events.
  // To fix this problem, we setup a fake scroll event. We say:
  // > If a scroll or touchmove event has happened in the last DELAY milliseconds, 
  //   then send a `$scroll` event every animationFrame.
  // Additionally, we add $scrollstart and $scrollend events.
  function setupAugmentedScrollEvents(element) {
    var SCROLL_END_DELAY = 200;
    var isScrolling;
    var lastScrollTime;
    element.on('scroll touchmove', function() {
      if (!isScrolling) {
        isScrolling = true;
        $$rAF.throttle(loopScrollEvent);
        element.triggerHandler('$scrollstart');
      }
      element.triggerHandler('$scroll');
      lastScrollTime = +$mdUtil.now();
    });

    function loopScrollEvent() {
      if (+$mdUtil.now() - lastScrollTime > SCROLL_END_DELAY) {
        isScrolling = false;
        element.triggerHandler('$scrollend');
      } else {
        element.triggerHandler('$scroll');
        $$rAF.throttle(loopScrollEvent);
      }
    }
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.subheader
 * @description
 * SubHeader module
 *
 *  Subheaders are special list tiles that delineate distinct sections of a
 *  list or grid list and are typically related to the current filtering or
 *  sorting criteria. Subheader tiles are either displayed inline with tiles or
 *  can be associated with content, for example, in an adjacent column.
 *
 *  Upon scrolling, subheaders remain pinned to the top of the screen and remain
 *  pinned until pushed on or off screen by the next subheader. @see [Material
 *  Design Specifications](https://www.google.com/design/spec/components/subheaders.html)
 *
 *  > To improve the visual grouping of content, use the system color for your subheaders.
 *
 */
MdSubheaderDirective.$inject = ["$mdSticky", "$compile", "$mdTheming", "$mdUtil", "$mdAria"];
angular
  .module('material.components.subheader', [
    'material.core',
    'material.components.sticky'
  ])
  .directive('mdSubheader', MdSubheaderDirective);

/**
 * @ngdoc directive
 * @name mdSubheader
 * @module material.components.subheader
 *
 * @restrict E
 *
 * @description
 * The `md-subheader` directive creates a sticky subheader for a section.
 *
 * Developers are able to disable the stickiness of the subheader by using the following markup
 *
 * <hljs lang="html">
 *   <md-subheader class="md-no-sticky">Not Sticky</md-subheader>
 * </hljs>
 *
 * ### Notes
 * - The `md-subheader` directive uses the <a ng-href="api/service/$mdSticky">$mdSticky</a> service
 * to make the subheader sticky.
 *
 * > Whenever the current browser doesn't support stickiness natively, the subheader
 * will be compiled twice to create a sticky clone of the subheader.
 *
 * @usage
 * <hljs lang="html">
 * <md-subheader>Online Friends</md-subheader>
 * </hljs>
 */

function MdSubheaderDirective($mdSticky, $compile, $mdTheming, $mdUtil, $mdAria) {
  return {
    restrict: 'E',
    replace: true,
    transclude: true,
    template: (
    '<div class="md-subheader _md">' +
    '  <div class="md-subheader-inner">' +
    '    <div class="md-subheader-content"></div>' +
    '  </div>' +
    '</div>'
    ),
    link: function postLink(scope, element, attr, controllers, transclude) {
      $mdTheming(element);
      element.addClass('_md');

      // Remove the ngRepeat attribute from the root element, because we don't want to compile
      // the ngRepeat for the sticky clone again.
      $mdUtil.prefixer().removeAttribute(element, 'ng-repeat');

      var outerHTML = element[0].outerHTML;

      function getContent(el) {
        return angular.element(el[0].querySelector('.md-subheader-content'));
      }

      // Set the ARIA attributes on the original element since it keeps it's original place in
      // the DOM, whereas the clones are in reverse order. Should be done after the outerHTML,
      // in order to avoid having multiple element be marked as headers.
      attr.$set('role', 'heading');
      $mdAria.expect(element, 'aria-level', '2');

      // Transclude the user-given contents of the subheader
      // the conventional way.
      transclude(scope, function(clone) {
        getContent(element).append(clone);
      });

      // Create another clone, that uses the outer and inner contents
      // of the element, that will be 'stickied' as the user scrolls.
      if (!element.hasClass('md-no-sticky')) {
        transclude(scope, function(clone) {
          // If the user adds an ng-if or ng-repeat directly to the md-subheader element, the
          // compiled clone below will only be a comment tag (since they replace their elements with
          // a comment) which cannot be properly passed to the $mdSticky; so we wrap it in our own
          // DIV to ensure we have something $mdSticky can use
          var wrapper = $compile('<div class="md-subheader-wrapper" aria-hidden="true">' + outerHTML + '</div>')(scope);

          // Delay initialization until after any `ng-if`/`ng-repeat`/etc has finished before
          // attempting to create the clone
          $mdUtil.nextTick(function() {
            // Append our transcluded clone into the wrapper.
            // We don't have to recompile the element again, because the clone is already
            // compiled in it's transclusion scope. If we recompile the outerHTML of the new clone, we would lose
            // our ngIf's and other previous registered bindings / properties.
            getContent(wrapper).append(clone);
          });

          // Make the element sticky and provide the stickyClone our self, to avoid recompilation of the subheader
          // element.
          $mdSticky(scope, element, wrapper);
        });
      }
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.swipe
 * @description Swipe module!
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeLeft
 *
 * @restrict A
 *
 * @description
 * The md-swipe-left directive allows you to specify custom behavior when an element is swiped
 * left.
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-left="onSwipeLeft()">Swipe me left!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeRight
 *
 * @restrict A
 *
 * @description
 * The md-swipe-right directive allows you to specify custom behavior when an element is swiped
 * right.
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-right="onSwipeRight()">Swipe me right!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeUp
 *
 * @restrict A
 *
 * @description
 * The md-swipe-up directive allows you to specify custom behavior when an element is swiped
 * up.
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-up="onSwipeUp()">Swipe me up!</div>
 * </hljs>
 */
/**
 * @ngdoc directive
 * @module material.components.swipe
 * @name mdSwipeDown
 *
 * @restrict A
 *
 * @description
 * The md-swipe-down directive allows you to specify custom behavior when an element is swiped
 * down.
 *
 * @usage
 * <hljs lang="html">
 * <div md-swipe-down="onSwipDown()">Swipe me down!</div>
 * </hljs>
 */

angular.module('material.components.swipe', ['material.core'])
    .directive('mdSwipeLeft', getDirective('SwipeLeft'))
    .directive('mdSwipeRight', getDirective('SwipeRight'))
    .directive('mdSwipeUp', getDirective('SwipeUp'))
    .directive('mdSwipeDown', getDirective('SwipeDown'));

function getDirective(name) {
    DirectiveFactory.$inject = ["$parse"];
  var directiveName = 'md' + name;
  var eventName = '$md.' + name.toLowerCase();

  return DirectiveFactory;

  /* @ngInject */
  function DirectiveFactory($parse) {
      return { restrict: 'A', link: postLink };
      function postLink(scope, element, attr) {
        var fn = $parse(attr[directiveName]);
        element.on(eventName, function(ev) {
          scope.$applyAsync(function() { fn(scope, { $event: ev }); });
        });
      }
    }
}



})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.switch
 */

MdSwitch.$inject = ["mdCheckboxDirective", "$mdUtil", "$mdConstant", "$parse", "$$rAF", "$mdGesture", "$timeout"];
angular.module('material.components.switch', [
  'material.core',
  'material.components.checkbox'
])
  .directive('mdSwitch', MdSwitch);

/**
 * @ngdoc directive
 * @module material.components.switch
 * @name mdSwitch
 * @restrict E
 *
 * The switch directive is used very much like the normal [angular checkbox](https://docs.angularjs.org/api/ng/input/input%5Bcheckbox%5D).
 *
 * As per the [material design spec](http://www.google.com/design/spec/style/color.html#color-ui-color-application)
 * the switch is in the accent color by default. The primary color palette may be used with
 * the `md-primary` class.
 *
 * @param {string} ng-model Assignable angular expression to data-bind to.
 * @param {string=} name Property name of the form under which the control is published.
 * @param {expression=} ng-true-value The value to which the expression should be set when selected.
 * @param {expression=} ng-false-value The value to which the expression should be set when not selected.
 * @param {string=} ng-change AngularJS expression to be executed when input changes due to user interaction with the input element.
 * @param {expression=} ng-disabled En/Disable based on the expression.
 * @param {boolean=} md-no-ink Use of attribute indicates use of ripple ink effects.
 * @param {string=} aria-label Publish the button label used by screen-readers for accessibility. Defaults to the switch's text.
 * @param {boolean=} md-invert When set to true, the switch will be inverted.
 *
 * @usage
 * <hljs lang="html">
 * <md-switch ng-model="isActive" aria-label="Finished?">
 *   Finished ?
 * </md-switch>
 *
 * <md-switch md-no-ink ng-model="hasInk" aria-label="No Ink Effects">
 *   No Ink Effects
 * </md-switch>
 *
 * <md-switch ng-disabled="true" ng-model="isDisabled" aria-label="Disabled">
 *   Disabled
 * </md-switch>
 *
 * </hljs>
 */
function MdSwitch(mdCheckboxDirective, $mdUtil, $mdConstant, $parse, $$rAF, $mdGesture, $timeout) {
  var checkboxDirective = mdCheckboxDirective[0];

  return {
    restrict: 'E',
    priority: $mdConstant.BEFORE_NG_ARIA,
    transclude: true,
    template:
      '<div class="md-container">' +
        '<div class="md-bar"></div>' +
        '<div class="md-thumb-container">' +
          '<div class="md-thumb" md-ink-ripple md-ink-ripple-checkbox></div>' +
        '</div>'+
      '</div>' +
      '<div ng-transclude class="md-label"></div>',
    require: ['^?mdInputContainer', '?ngModel', '?^form'],
    compile: mdSwitchCompile
  };

  function mdSwitchCompile(element, attr) {
    var checkboxLink = checkboxDirective.compile(element, attr).post;
    // No transition on initial load.
    element.addClass('md-dragging');

    return function (scope, element, attr, ctrls) {
      var containerCtrl = ctrls[0];
      var ngModel = ctrls[1] || $mdUtil.fakeNgModel();
      var formCtrl = ctrls[2];

      var disabledGetter = null;
      if (attr.disabled != null) {
        disabledGetter = function() { return true; };
      } else if (attr.ngDisabled) {
        disabledGetter = $parse(attr.ngDisabled);
      }

      var thumbContainer = angular.element(element[0].querySelector('.md-thumb-container'));
      var switchContainer = angular.element(element[0].querySelector('.md-container'));
      var labelContainer = angular.element(element[0].querySelector('.md-label'));

      // no transition on initial load
      $$rAF(function() {
        element.removeClass('md-dragging');
      });

      checkboxLink(scope, element, attr, ctrls);

      if (disabledGetter) {
        scope.$watch(disabledGetter, function(isDisabled) {
          element.attr('tabindex', isDisabled ? -1 : 0);
        });
      }

      attr.$observe('mdInvert', function(newValue) {
        var isInverted = $mdUtil.parseAttributeBoolean(newValue);

        isInverted ? element.prepend(labelContainer) : element.prepend(switchContainer);

        // Toggle a CSS class to update the margin.
        element.toggleClass('md-inverted', isInverted);
      });

      // These events are triggered by setup drag
      $mdGesture.register(switchContainer, 'drag');
      switchContainer
        .on('$md.dragstart', onDragStart)
        .on('$md.drag', onDrag)
        .on('$md.dragend', onDragEnd);

      var drag;
      function onDragStart(ev) {
        // Don't go if the switch is disabled.
        if (disabledGetter && disabledGetter(scope)) return;
        ev.stopPropagation();

        element.addClass('md-dragging');
        drag = {width: thumbContainer.prop('offsetWidth')};
      }

      function onDrag(ev) {
        if (!drag) return;
        ev.stopPropagation();
        ev.srcEvent && ev.srcEvent.preventDefault();

        var percent = ev.pointer.distanceX / drag.width;

        //if checked, start from right. else, start from left
        var translate = ngModel.$viewValue ?  1 + percent : percent;
        // Make sure the switch stays inside its bounds, 0-1%
        translate = Math.max(0, Math.min(1, translate));

        thumbContainer.css($mdConstant.CSS.TRANSFORM, 'translate3d(' + (100*translate) + '%,0,0)');
        drag.translate = translate;
      }

      function onDragEnd(ev) {
        if (!drag) return;
        ev.stopPropagation();

        element.removeClass('md-dragging');
        thumbContainer.css($mdConstant.CSS.TRANSFORM, '');

        // We changed if there is no distance (this is a click a click),
        // or if the drag distance is >50% of the total.
        var isChanged = ngModel.$viewValue ? drag.translate < 0.5 : drag.translate > 0.5;
        if (isChanged) {
          applyModelValue(!ngModel.$viewValue);
        }
        drag = null;

        // Wait for incoming mouse click
        scope.skipToggle = true;
        $timeout(function() {
          scope.skipToggle = false;
        }, 1);
      }

      function applyModelValue(newValue) {
        scope.$apply(function() {
          ngModel.$setViewValue(newValue);
          ngModel.$render();
        });
      }

    };
  }


}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.tabs
 * @description
 *
 *  Tabs, created with the `<md-tabs>` directive provide *tabbed* navigation with different styles.
 *  The Tabs component consists of clickable tabs that are aligned horizontally side-by-side.
 *
 *  Features include support for:
 *
 *  - static or dynamic tabs,
 *  - responsive designs,
 *  - accessibility support (ARIA),
 *  - tab pagination,
 *  - external or internal tab content,
 *  - focus indicators and arrow-key navigations,
 *  - programmatic lookup and access to tab controllers, and
 *  - dynamic transitions through different tab contents.
 *
 */
/*
 * @see js folder for tabs implementation
 */
angular.module('material.components.tabs', [
  'material.core',
  'material.components.icon'
]);

})();
(function(){
"use strict";

angular
.module('material.components.tabs')
.service('MdTabsPaginationService', MdTabsPaginationService);

/**
 * @private
 * @module material.components.tabs
 * @name MdTabsPaginationService
 * @description Provides many standalone functions to ease in pagination calculations.
 *
 * Most functions accept the elements and the current offset.
 *
 * The `elements` parameter is typically the value returned from the `getElements()` function of the
 * tabsController.
 *
 * The `offset` parameter is always positive regardless of LTR or RTL (we simply make the LTR one
 * negative when we apply our transform). This is typically the `ctrl.leftOffset` variable in the
 * tabsController.
 *
 * @returns MdTabsPaginationService
 * @constructor
 */
function MdTabsPaginationService() {
  return {
    decreasePageOffset: decreasePageOffset,
    increasePageOffset: increasePageOffset,
    getTabOffsets: getTabOffsets,
    getTotalTabsWidth: getTotalTabsWidth
  };

  /**
   * Returns the offset for the next decreasing page.
   *
   * @param elements
   * @param currentOffset
   * @returns {number}
   */
  function decreasePageOffset(elements, currentOffset) {
    var canvas       = elements.canvas,
        tabOffsets   = getTabOffsets(elements),
        i, firstVisibleTabOffset;

    // Find the first fully visible tab in offset range
    for (i = 0; i < tabOffsets.length; i++) {
      if (tabOffsets[i] >= currentOffset) {
        firstVisibleTabOffset = tabOffsets[i];
        break;
      }
    }

    // Return (the first visible tab offset - the tabs container width) without going negative
    return Math.max(0, firstVisibleTabOffset - canvas.clientWidth);
  }

  /**
   * Returns the offset for the next increasing page.
   *
   * @param elements
   * @param currentOffset
   * @returns {number}
   */
  function increasePageOffset(elements, currentOffset) {
    var canvas       = elements.canvas,
        maxOffset    = getTotalTabsWidth(elements) - canvas.clientWidth,
        tabOffsets   = getTabOffsets(elements),
        i, firstHiddenTabOffset;

    // Find the first partially (or fully) invisible tab
    for (i = 0; i < tabOffsets.length, tabOffsets[i] <= currentOffset + canvas.clientWidth; i++) {
      firstHiddenTabOffset = tabOffsets[i];
    }

    // Return the offset of the first hidden tab, or the maximum offset (whichever is smaller)
    return Math.min(maxOffset, firstHiddenTabOffset);
  }

  /**
   * Returns the offsets of all of the tabs based on their widths.
   *
   * @param elements
   * @returns {number[]}
   */
  function getTabOffsets(elements) {
    var i, tab, currentOffset = 0, offsets = [];

    for (i = 0; i < elements.tabs.length; i++) {
      tab = elements.tabs[i];
      offsets.push(currentOffset);
      currentOffset += tab.offsetWidth;
    }

    return offsets;
  }

  /**
   * Sum the width of all tabs.
   *
   * @param elements
   * @returns {number}
   */
  function getTotalTabsWidth(elements) {
    var sum = 0, i, tab;

    for (i = 0; i < elements.tabs.length; i++) {
      tab = elements.tabs[i];
      sum += tab.offsetWidth;
    }

    return sum;
  }

}

})();
(function(){
"use strict";

/**
  * @ngdoc module
  * @name material.components.toast
  * @description
  * Toast
  */
MdToastDirective.$inject = ["$mdToast"];
MdToastProvider.$inject = ["$$interimElementProvider"];
angular.module('material.components.toast', [
  'material.core',
  'material.components.button'
])
  .directive('mdToast', MdToastDirective)
  .provider('$mdToast', MdToastProvider);

/* @ngInject */
function MdToastDirective($mdToast) {
  return {
    restrict: 'E',
    link: function postLink(scope, element) {
      element.addClass('_md');     // private md component indicator for styling
      
      // When navigation force destroys an interimElement, then
      // listen and $destroy() that interim instance...
      scope.$on('$destroy', function() {
        $mdToast.destroy();
      });
    }
  };
}

/**
  * @ngdoc service
  * @name $mdToast
  * @module material.components.toast
  *
  * @description
  * `$mdToast` is a service to build a toast notification on any position
  * on the screen with an optional duration, and provides a simple promise API.
  *
  * The toast will be always positioned at the `bottom`, when the screen size is
  * between `600px` and `959px` (`sm` breakpoint)
  *
  * ## Restrictions on custom toasts
  * - The toast's template must have an outer `<md-toast>` element.
  * - For a toast action, use element with class `md-action`.
  * - Add the class `md-capsule` for curved corners.
  *
  * ### Custom Presets
  * Developers are also able to create their own preset, which can be easily used without repeating
  * their options each time.
  *
  * <hljs lang="js">
  *   $mdToastProvider.addPreset('testPreset', {
  *     options: function() {
  *       return {
  *         template:
  *           '<md-toast>' +
  *             '<div class="md-toast-content">' +
  *               'This is a custom preset' +
  *             '</div>' +
  *           '</md-toast>',
  *         controllerAs: 'toast',
  *         bindToController: true
  *       };
  *     }
  *   });
  * </hljs>
  *
  * After you created your preset at config phase, you can easily access it.
  *
  * <hljs lang="js">
  *   $mdToast.show(
  *     $mdToast.testPreset()
  *   );
  * </hljs>
  *
  * ## Parent container notes
  *
  * The toast is positioned using absolute positioning relative to its first non-static parent
  * container. Thus, if the requested parent container uses static positioning, we will temporarily
  * set its positioning to `relative` while the toast is visible and reset it when the toast is
  * hidden.
  *
  * Because of this, it is usually best to ensure that the parent container has a fixed height and
  * prevents scrolling by setting the `overflow: hidden;` style. Since the position is based off of
  * the parent's height, the toast may be mispositioned if you allow the parent to scroll.
  *
  * You can, however, have a scrollable element inside of the container; just make sure the
  * container itself does not scroll.
  *
  * <hljs lang="html">
  * <div layout-fill id="toast-container">
  *   <md-content>
  *     I can have lots of content and scroll!
  *   </md-content>
  * </div>
  * </hljs>
  *
  * @usage
  * <hljs lang="html">
  * <div ng-controller="MyController">
  *   <md-button ng-click="openToast()">
  *     Open a Toast!
  *   </md-button>
  * </div>
  * </hljs>
  *
  * <hljs lang="js">
  * var app = angular.module('app', ['ngMaterial']);
  * app.controller('MyController', function($scope, $mdToast) {
  *   $scope.openToast = function($event) {
  *     $mdToast.show($mdToast.simple().textContent('Hello!'));
  *     // Could also do $mdToast.showSimple('Hello');
  *   };
  * });
  * </hljs>
  */

/**
 * @ngdoc method
 * @name $mdToast#showSimple
 * 
 * @param {string} message The message to display inside the toast
 * @description
 * Convenience method which builds and shows a simple toast.
 *
 * @returns {promise} A promise that can be resolved with `$mdToast.hide()` or
 * rejected with `$mdToast.cancel()`.
 *
 */

 /**
  * @ngdoc method
  * @name $mdToast#simple
  *
  * @description
  * Builds a preconfigured toast.
  *
  * @returns {obj} a `$mdToastPreset` with the following chainable configuration methods.
  *
  * _**Note:** These configuration methods are provided in addition to the methods provided by
  * the `build()` and `show()` methods below._
  *
  * <table class="md-api-table methods">
  *    <thead>
  *      <tr>
  *        <th>Method</th>
  *        <th>Description</th>
  *      </tr>
  *    </thead>
  *    <tbody>
  *      <tr>
  *        <td>`.textContent(string)`</td>
  *        <td>Sets the toast content to the specified string</td>
  *      </tr>
  *      <tr>
  *        <td>`.action(string)`</td>
  *        <td>
  *          Adds an action button. <br/>
  *          If clicked, the promise (returned from `show()`)
  *          will resolve with the value `'ok'`; otherwise, it is resolved with `true` after a `hideDelay`
  *          timeout
  *        </td>
  *      </tr>
  *      <tr>
  *        <td>`.highlightAction(boolean)`</td>
  *        <td>
  *          Whether or not the action button will have an additional highlight class.<br/>
  *          By default the `accent` color will be applied to the action button.
  *        </td>
  *      </tr>
  *      <tr>
  *        <td>`.highlightClass(string)`</td>
  *        <td>
  *          If set, the given class will be applied to the highlighted action button.<br/>
  *          This allows you to specify the highlight color easily. Highlight classes are `md-primary`, `md-warn`
  *          and `md-accent`
  *        </td>
  *      </tr>
  *      <tr>
  *        <td>`.capsule(boolean)`</td>
  *        <td>Whether or not to add the `md-capsule` class to the toast to provide rounded corners</td>
  *      </tr>
  *      <tr>
  *        <td>`.theme(string)`</td>
  *        <td>Sets the theme on the toast to the requested theme. Default is `$mdThemingProvider`'s default.</td>
  *      </tr>
  *      <tr>
  *        <td>`.toastClass(string)`</td>
  *        <td>Sets a class on the toast element</td>
  *      </tr>
  *    </tbody>
  * </table>
  *
  */

/**
  * @ngdoc method
  * @name $mdToast#updateTextContent
  *
  * @description
  * Updates the content of an existing toast. Useful for updating things like counts, etc.
  *
  */

 /**
  * @ngdoc method
  * @name $mdToast#build
  *
  * @description
  * Creates a custom `$mdToastPreset` that you can configure.
  *
  * @returns {obj} a `$mdToastPreset` with the chainable configuration methods for shows' options (see below).
  */

 /**
  * @ngdoc method
  * @name $mdToast#show
  *
  * @description Shows the toast.
  *
  * @param {object} optionsOrPreset Either provide an `$mdToastPreset` returned from `simple()`
  * and `build()`, or an options object with the following properties:
  *
  *   - `templateUrl` - `{string=}`: The url of an html template file that will
  *     be used as the content of the toast. Restrictions: the template must
  *     have an outer `md-toast` element.
  *   - `template` - `{string=}`: Same as templateUrl, except this is an actual
  *     template string.
  *   - `autoWrap` - `{boolean=}`: Whether or not to automatically wrap the template content with a
  *     `<div class="md-toast-content">` if one is not provided. Defaults to true. Can be disabled if you provide a
  *     custom toast directive.
  *   - `scope` - `{object=}`: the scope to link the template / controller to. If none is specified, it will create a new child scope.
  *     This scope will be destroyed when the toast is removed unless `preserveScope` is set to true.
  *   - `preserveScope` - `{boolean=}`: whether to preserve the scope when the element is removed. Default is false
  *   - `hideDelay` - `{number=}`: How many milliseconds the toast should stay
  *     active before automatically closing.  Set to 0 or false to have the toast stay open until
  *     closed manually. Default: 3000.
  *   - `position` - `{string=}`: Sets the position of the toast. <br/>
  *     Available: any combination of `'bottom'`, `'left'`, `'top'`, `'right'`, `'end'` and `'start'`.
  *     The properties `'end'` and `'start'` are dynamic and can be used for RTL support.<br/>
  *     Default combination: `'bottom left'`.
  *   - `toastClass` - `{string=}`: A class to set on the toast element.
  *   - `controller` - `{string=}`: The controller to associate with this toast.
  *     The controller will be injected the local `$mdToast.hide( )`, which is a function
  *     used to hide the toast.
  *   - `locals` - `{string=}`: An object containing key/value pairs. The keys will
  *     be used as names of values to inject into the controller. For example,
  *     `locals: {three: 3}` would inject `three` into the controller with the value
  *     of 3.
  *   - `bindToController` - `bool`: bind the locals to the controller, instead of passing them in.
  *   - `resolve` - `{object=}`: Similar to locals, except it takes promises as values
  *     and the toast will not open until the promises resolve.
  *   - `controllerAs` - `{string=}`: An alias to assign the controller to on the scope.
  *   - `parent` - `{element=}`: The element to append the toast to. Defaults to appending
  *     to the root element of the application.
  *
  * @returns {promise} A promise that can be resolved with `$mdToast.hide()` or
  * rejected with `$mdToast.cancel()`. `$mdToast.hide()` will resolve either with a Boolean
  * value == 'true' or the value passed as an argument to `$mdToast.hide()`.
  * And `$mdToast.cancel()` will resolve the promise with a Boolean value == 'false'
  */

/**
  * @ngdoc method
  * @name $mdToast#hide
  *
  * @description
  * Hide an existing toast and resolve the promise returned from `$mdToast.show()`.
  *
  * @param {*=} response An argument for the resolved promise.
  *
  * @returns {promise} a promise that is called when the existing element is removed from the DOM.
  * The promise is resolved with either a Boolean value == 'true' or the value passed as the
  * argument to `.hide()`.
  *
  */

/**
  * @ngdoc method
  * @name $mdToast#cancel
  *
  * @description
  * `DEPRECATED` - The promise returned from opening a toast is used only to notify about the closing of the toast.
  * As such, there isn't any reason to also allow that promise to be rejected,
  * since it's not clear what the difference between resolve and reject would be.
  *
  * Hide the existing toast and reject the promise returned from
  * `$mdToast.show()`.
  *
  * @param {*=} response An argument for the rejected promise.
  *
  * @returns {promise} a promise that is called when the existing element is removed from the DOM
  * The promise is resolved with a Boolean value == 'false'.
  *
  */

function MdToastProvider($$interimElementProvider) {
  // Differentiate promise resolves: hide timeout (value == true) and hide action clicks (value == ok).
  MdToastController.$inject = ["$mdToast", "$scope"];
  toastDefaultOptions.$inject = ["$animate", "$mdToast", "$mdUtil", "$mdMedia"];
  var ACTION_RESOLVE = 'ok';

  var activeToastContent;
  var $mdToast = $$interimElementProvider('$mdToast')
    .setDefaults({
      methods: ['position', 'hideDelay', 'capsule', 'parent', 'position', 'toastClass'],
      options: toastDefaultOptions
    })
    .addPreset('simple', {
      argOption: 'textContent',
      methods: ['textContent', 'content', 'action', 'highlightAction', 'highlightClass', 'theme', 'parent' ],
      options: /* @ngInject */ ["$mdToast", "$mdTheming", function($mdToast, $mdTheming) {
        return {
          template:
            '<md-toast md-theme="{{ toast.theme }}" ng-class="{\'md-capsule\': toast.capsule}">' +
            '  <div class="md-toast-content">' +
            '    <span class="md-toast-text" role="alert" aria-relevant="all" aria-atomic="true">' +
            '      {{ toast.content }}' +
            '    </span>' +
            '    <md-button class="md-action" ng-if="toast.action" ng-click="toast.resolve()" ' +
            '        ng-class="highlightClasses">' +
            '      {{ toast.action }}' +
            '    </md-button>' +
            '  </div>' +
            '</md-toast>',
          controller: MdToastController,
          theme: $mdTheming.defaultTheme(),
          controllerAs: 'toast',
          bindToController: true
        };
      }]
    })
    .addMethod('updateTextContent', updateTextContent)
    .addMethod('updateContent', updateTextContent);

    function updateTextContent(newContent) {
      activeToastContent = newContent;
    }

    return $mdToast;

  /**
   * Controller for the Toast interim elements.
   * @ngInject
   */
  function MdToastController($mdToast, $scope) {
    // For compatibility with AngularJS 1.6+, we should always use the $onInit hook in
    // interimElements. The $mdCompiler simulates the $onInit hook for all versions.
    this.$onInit = function() {
      var self = this;

      if (self.highlightAction) {
        $scope.highlightClasses = [
          'md-highlight',
          self.highlightClass
        ]
      }

      $scope.$watch(function() { return activeToastContent; }, function() {
        self.content = activeToastContent;
      });

      this.resolve = function() {
        $mdToast.hide( ACTION_RESOLVE );
      };
    }
  }

  /* @ngInject */
  function toastDefaultOptions($animate, $mdToast, $mdUtil, $mdMedia) {
    var SWIPE_EVENTS = '$md.swipeleft $md.swiperight $md.swipeup $md.swipedown';
    return {
      onShow: onShow,
      onRemove: onRemove,
      toastClass: '',
      position: 'bottom left',
      themable: true,
      hideDelay: 3000,
      autoWrap: true,
      transformTemplate: function(template, options) {
        var shouldAddWrapper = options.autoWrap && template && !/md-toast-content/g.test(template);

        if (shouldAddWrapper) {
          // Root element of template will be <md-toast>. We need to wrap all of its content inside of
          // of <div class="md-toast-content">. All templates provided here should be static, developer-controlled
          // content (meaning we're not attempting to guard against XSS).
          var templateRoot = document.createElement('md-template');
          templateRoot.innerHTML = template;

          // Iterate through all root children, to detect possible md-toast directives.
          for (var i = 0; i < templateRoot.children.length; i++) {
            if (templateRoot.children[i].nodeName === 'MD-TOAST') {
              var wrapper = angular.element('<div class="md-toast-content">');

              // Wrap the children of the `md-toast` directive in jqLite, to be able to append multiple
              // nodes with the same execution.
              wrapper.append(angular.element(templateRoot.children[i].childNodes));

              // Append the new wrapped element to the `md-toast` directive.
              templateRoot.children[i].appendChild(wrapper[0]);
            }
          }

          // We have to return the innerHTMl, because we do not want to have the `md-template` element to be
          // the root element of our interimElement.
          return templateRoot.innerHTML;
        }

        return template || '';
      }
    };

    function onShow(scope, element, options) {
      activeToastContent = options.textContent || options.content; // support deprecated #content method

      var isSmScreen = !$mdMedia('gt-sm');

      element = $mdUtil.extractElementByName(element, 'md-toast', true);
      options.element = element;

      options.onSwipe = function(ev, gesture) {
        //Add the relevant swipe class to the element so it can animate correctly
        var swipe = ev.type.replace('$md.','');
        var direction = swipe.replace('swipe', '');

        // If the swipe direction is down/up but the toast came from top/bottom don't fade away
        // Unless the screen is small, then the toast always on bottom
        if ((direction === 'down' && options.position.indexOf('top') != -1 && !isSmScreen) ||
            (direction === 'up' && (options.position.indexOf('bottom') != -1 || isSmScreen))) {
          return;
        }

        if ((direction === 'left' || direction === 'right') && isSmScreen) {
          return;
        }

        element.addClass('md-' + swipe);
        $mdUtil.nextTick($mdToast.cancel);
      };
      options.openClass = toastOpenClass(options.position);

      element.addClass(options.toastClass);

      // 'top left' -> 'md-top md-left'
      options.parent.addClass(options.openClass);

      // static is the default position
      if ($mdUtil.hasComputedStyle(options.parent, 'position', 'static')) {
        options.parent.css('position', 'relative');
      }

      element.on(SWIPE_EVENTS, options.onSwipe);
      element.addClass(isSmScreen ? 'md-bottom' : options.position.split(' ').map(function(pos) {
        return 'md-' + pos;
      }).join(' '));

      if (options.parent) options.parent.addClass('md-toast-animating');
      return $animate.enter(element, options.parent).then(function() {
        if (options.parent) options.parent.removeClass('md-toast-animating');
      });
    }

    function onRemove(scope, element, options) {
      element.off(SWIPE_EVENTS, options.onSwipe);
      if (options.parent) options.parent.addClass('md-toast-animating');
      if (options.openClass) options.parent.removeClass(options.openClass);

      return ((options.$destroy == true) ? element.remove() : $animate.leave(element))
        .then(function () {
          if (options.parent) options.parent.removeClass('md-toast-animating');
          if ($mdUtil.hasComputedStyle(options.parent, 'position', 'static')) {
            options.parent.css('position', '');
          }
        });
    }

    function toastOpenClass(position) {
      // For mobile, always open full-width on bottom
      if (!$mdMedia('gt-xs')) {
        return 'md-toast-open-bottom';
      }

      return 'md-toast-open-' +
        (position.indexOf('top') > -1 ? 'top' : 'bottom');
    }
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.toolbar
 */
mdToolbarDirective.$inject = ["$$rAF", "$mdConstant", "$mdUtil", "$mdTheming", "$animate"];
angular.module('material.components.toolbar', [
  'material.core',
  'material.components.content'
])
  .directive('mdToolbar', mdToolbarDirective);

/**
 * @ngdoc directive
 * @name mdToolbar
 * @module material.components.toolbar
 * @restrict E
 * @description
 * `md-toolbar` is used to place a toolbar in your app.
 *
 * Toolbars are usually used above a content area to display the title of the
 * current page, and show relevant action buttons for that page.
 *
 * You can change the height of the toolbar by adding either the
 * `md-medium-tall` or `md-tall` class to the toolbar.
 *
 * @usage
 * <hljs lang="html">
 * <div layout="column" layout-fill>
 *   <md-toolbar>
 *
 *     <div class="md-toolbar-tools">
 *       <h2 md-truncate flex>My App's Title</h2>
 *
 *       <md-button>
 *         Right Bar Button
 *       </md-button>
 *     </div>
 *
 *   </md-toolbar>
 *   <md-content>
 *     Hello!
 *   </md-content>
 * </div>
 * </hljs>
 *
 * <i><b>Note:</b> The code above shows usage with the `md-truncate` component which provides an
 * ellipsis if the title is longer than the width of the Toolbar.</i>
 *
 * ## CSS & Styles
 *
 * The `<md-toolbar>` provides a few custom CSS classes that you may use to enhance the
 * functionality of your toolbar.
 *
 * <div>
 * <docs-css-api-table>
 *
 *   <docs-css-selector code="md-toolbar .md-toolbar-tools">
 *     The `md-toolbar-tools` class provides quite a bit of automatic styling for your toolbar
 *     buttons and text. When applied, it will center the buttons and text vertically for you.
 *   </docs-css-selector>
 *
 * </docs-css-api-table>
 * </div>
 *
 * ### Private Classes
 *
 * Currently, the only private class is the `md-toolbar-transitions` class. All other classes are
 * considered public.
 *
 * @param {boolean=} md-scroll-shrink Whether the header should shrink away as
 * the user scrolls down, and reveal itself as the user scrolls up.
 *
 * _**Note (1):** for scrollShrink to work, the toolbar must be a sibling of a
 * `md-content` element, placed before it. See the scroll shrink demo._
 *
 * _**Note (2):** The `md-scroll-shrink` attribute is only parsed on component
 * initialization, it does not watch for scope changes._
 *
 *
 * @param {number=} md-shrink-speed-factor How much to change the speed of the toolbar's
 * shrinking by. For example, if 0.25 is given then the toolbar will shrink
 * at one fourth the rate at which the user scrolls down. Default 0.5.
 *
 */

function mdToolbarDirective($$rAF, $mdConstant, $mdUtil, $mdTheming, $animate) {
  var translateY = angular.bind(null, $mdUtil.supplant, 'translate3d(0,{0}px,0)');

  return {
    template: '',
    restrict: 'E',

    link: function(scope, element, attr) {

      element.addClass('_md');     // private md component indicator for styling
      $mdTheming(element);

      $mdUtil.nextTick(function () {
        element.addClass('_md-toolbar-transitions');     // adding toolbar transitions after digest
      }, false);

      if (angular.isDefined(attr.mdScrollShrink)) {
        setupScrollShrink();
      }

      function setupScrollShrink() {

        var toolbarHeight;
        var contentElement;
        var disableScrollShrink = angular.noop;

        // Current "y" position of scroll
        // Store the last scroll top position
        var y = 0;
        var prevScrollTop = 0;
        var shrinkSpeedFactor = attr.mdShrinkSpeedFactor || 0.5;

        var debouncedContentScroll = $$rAF.throttle(onContentScroll);
        var debouncedUpdateHeight = $mdUtil.debounce(updateToolbarHeight, 5 * 1000);

        // Wait for $mdContentLoaded event from mdContent directive.
        // If the mdContent element is a sibling of our toolbar, hook it up
        // to scroll events.

        scope.$on('$mdContentLoaded', onMdContentLoad);

        // If the toolbar is used inside an ng-if statement, we may miss the
        // $mdContentLoaded event, so we attempt to fake it if we have a
        // md-content close enough.

        attr.$observe('mdScrollShrink', onChangeScrollShrink);

        // If the toolbar has ngShow or ngHide we need to update height immediately as it changed
        // and not wait for $mdUtil.debounce to happen

        if (attr.ngShow) { scope.$watch(attr.ngShow, updateToolbarHeight); }
        if (attr.ngHide) { scope.$watch(attr.ngHide, updateToolbarHeight); }

        // If the scope is destroyed (which could happen with ng-if), make sure
        // to disable scroll shrinking again

        scope.$on('$destroy', disableScrollShrink);

        /**
         *
         */
        function onChangeScrollShrink(shrinkWithScroll) {
          var closestContent = element.parent().find('md-content');

          // If we have a content element, fake the call; this might still fail
          // if the content element isn't a sibling of the toolbar

          if (!contentElement && closestContent.length) {
            onMdContentLoad(null, closestContent);
          }

          // Evaluate the expression
          shrinkWithScroll = scope.$eval(shrinkWithScroll);

          // Disable only if the attribute's expression evaluates to false
          if (shrinkWithScroll === false) {
            disableScrollShrink();
          } else {
            disableScrollShrink = enableScrollShrink();
          }
        }

        /**
         *
         */
        function onMdContentLoad($event, newContentEl) {
          // Toolbar and content must be siblings
          if (newContentEl && element.parent()[0] === newContentEl.parent()[0]) {
            // unhook old content event listener if exists
            if (contentElement) {
              contentElement.off('scroll', debouncedContentScroll);
            }

            contentElement = newContentEl;
            disableScrollShrink = enableScrollShrink();
          }
        }

        /**
         *
         */
        function onContentScroll(e) {
          var scrollTop = e ? e.target.scrollTop : prevScrollTop;

          debouncedUpdateHeight();

          y = Math.min(
            toolbarHeight / shrinkSpeedFactor,
            Math.max(0, y + scrollTop - prevScrollTop)
          );

          element.css($mdConstant.CSS.TRANSFORM, translateY([-y * shrinkSpeedFactor]));
          contentElement.css($mdConstant.CSS.TRANSFORM, translateY([(toolbarHeight - y) * shrinkSpeedFactor]));

          prevScrollTop = scrollTop;

          $mdUtil.nextTick(function() {
            var hasWhiteFrame = element.hasClass('md-whiteframe-z1');

            if (hasWhiteFrame && !y) {
              $animate.removeClass(element, 'md-whiteframe-z1');
            } else if (!hasWhiteFrame && y) {
              $animate.addClass(element, 'md-whiteframe-z1');
            }
          });

        }

        /**
         *
         */
        function enableScrollShrink() {
          if (!contentElement)     return angular.noop;           // no md-content

          contentElement.on('scroll', debouncedContentScroll);
          contentElement.attr('scroll-shrink', 'true');

          $mdUtil.nextTick(updateToolbarHeight, false);

          return function disableScrollShrink() {
            contentElement.off('scroll', debouncedContentScroll);
            contentElement.attr('scroll-shrink', 'false');

            updateToolbarHeight();
          };
        }

        /**
         *
         */
        function updateToolbarHeight() {
          toolbarHeight = element.prop('offsetHeight');
          // Add a negative margin-top the size of the toolbar to the content el.
          // The content will start transformed down the toolbarHeight amount,
          // so everything looks normal.
          //
          // As the user scrolls down, the content will be transformed up slowly
          // to put the content underneath where the toolbar was.
          var margin = (-toolbarHeight * shrinkSpeedFactor) + 'px';

          contentElement.css({
            "margin-top": margin,
            "margin-bottom": margin
          });

          onContentScroll();
        }

      }

    }
  };

}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.tooltip
 */
MdTooltipDirective.$inject = ["$timeout", "$window", "$$rAF", "$document", "$interpolate", "$mdUtil", "$mdPanel", "$$mdTooltipRegistry"];
angular
    .module('material.components.tooltip', [
      'material.core',
      'material.components.panel'
    ])
    .directive('mdTooltip', MdTooltipDirective)
    .service('$$mdTooltipRegistry', MdTooltipRegistry);


/**
 * @ngdoc directive
 * @name mdTooltip
 * @module material.components.tooltip
 * @description
 * Tooltips are used to describe elements that are interactive and primarily
 * graphical (not textual).
 *
 * Place a `<md-tooltip>` as a child of the element it describes.
 *
 * A tooltip will activate when the user hovers over, focuses, or touches the
 * parent element.
 *
 * @usage
 * <hljs lang="html">
 *   <md-button class="md-fab md-accent" aria-label="Play">
 *     <md-tooltip>Play Music</md-tooltip>
 *     <md-icon md-svg-src="img/icons/ic_play_arrow_24px.svg"></md-icon>
 *   </md-button>
 * </hljs>
 *
 * @param {number=} md-z-index The visual level that the tooltip will appear
 *     in comparison with the rest of the elements of the application.
 * @param {expression=} md-visible Boolean bound to whether the tooltip is
 *     currently visible.
 * @param {number=} md-delay How many milliseconds to wait to show the tooltip
 *     after the user hovers over, focuses, or touches the parent element.
 *     Defaults to 0ms on non-touch devices and 75ms on touch.
 * @param {boolean=} md-autohide If present or provided with a boolean value,
 *     the tooltip will hide on mouse leave, regardless of focus.
 * @param {string=} md-direction The direction that the tooltip is shown,
 *     relative to the parent element. Supports top, right, bottom, and left.
 *     Defaults to bottom.
 */
function MdTooltipDirective($timeout, $window, $$rAF, $document, $interpolate,
    $mdUtil, $mdPanel, $$mdTooltipRegistry) {

  var ENTER_EVENTS = 'focus touchstart mouseenter';
  var LEAVE_EVENTS = 'blur touchcancel mouseleave';
  var TOOLTIP_DEFAULT_Z_INDEX = 100;
  var TOOLTIP_DEFAULT_SHOW_DELAY = 0;
  var TOOLTIP_DEFAULT_DIRECTION = 'bottom';
  var TOOLTIP_DIRECTIONS = {
    top: { x: $mdPanel.xPosition.CENTER, y: $mdPanel.yPosition.ABOVE },
    right: { x: $mdPanel.xPosition.OFFSET_END, y: $mdPanel.yPosition.CENTER },
    bottom: { x: $mdPanel.xPosition.CENTER, y: $mdPanel.yPosition.BELOW },
    left: { x: $mdPanel.xPosition.OFFSET_START, y: $mdPanel.yPosition.CENTER }
  };

  return {
    restrict: 'E',
    priority: 210, // Before ngAria
    scope: {
      mdZIndex: '=?mdZIndex',
      mdDelay: '=?mdDelay',
      mdVisible: '=?mdVisible',
      mdAutohide: '=?mdAutohide',
      mdDirection: '@?mdDirection' // Do not expect expressions.
    },
    link: linkFunc
  };

  function linkFunc(scope, element, attr) {
    // Set constants.
    var tooltipId = 'md-tooltip-' + $mdUtil.nextUid();
    var parent = $mdUtil.getParentWithPointerEvents(element);
    var debouncedOnResize = $$rAF.throttle(updatePosition);
    var mouseActive = false;
    var origin, position, panelPosition, panelRef, autohide, showTimeout,
        elementFocusedOnWindowBlur = null;

    // Set defaults
    setDefaults();

    // Set parent aria-label.
    addAriaLabel();

    // Remove the element from its current DOM position.
    element.detach();

    updatePosition();
    bindEvents();
    configureWatchers();

    function setDefaults() {
      scope.mdZIndex = scope.mdZIndex || TOOLTIP_DEFAULT_Z_INDEX;
      scope.mdDelay = scope.mdDelay || TOOLTIP_DEFAULT_SHOW_DELAY;
      if (!TOOLTIP_DIRECTIONS[scope.mdDirection]) {
        scope.mdDirection = TOOLTIP_DEFAULT_DIRECTION;
      }
    }

    function addAriaLabel(labelText) {
      // Only interpolate the text from the HTML element because otherwise the custom text could
      // be interpolated twice and cause XSS violations.
      var interpolatedText = labelText || $interpolate(element.text().trim())(scope.$parent);

      // Only add the `aria-label` to the parent if there isn't already one, if there isn't an
      // already present `aria-labelledby`, or if the previous `aria-label` was added by the
      // tooltip directive.
      if (
        (!parent.attr('aria-label') && !parent.attr('aria-labelledby')) ||
        parent.attr('md-labeled-by-tooltip')
      ) {
        parent.attr('aria-label', interpolatedText);

        // Set the `md-labeled-by-tooltip` attribute if it has not already been set.
        if (!parent.attr('md-labeled-by-tooltip')) {
          parent.attr('md-labeled-by-tooltip', tooltipId);
        }
      }
    }

    function updatePosition() {
      setDefaults();

      // If the panel has already been created, remove the current origin
      // class from the panel element.
      if (panelRef && panelRef.panelEl) {
        panelRef.panelEl.removeClass(origin);
      }

      // Set the panel element origin class based off of the current
      // mdDirection.
      origin = 'md-origin-' + scope.mdDirection;

      // Create the position of the panel based off of the mdDirection.
      position = TOOLTIP_DIRECTIONS[scope.mdDirection];

      // Using the newly created position object, use the MdPanel
      // panelPosition API to build the panel's position.
      panelPosition = $mdPanel.newPanelPosition()
          .relativeTo(parent)
          .addPanelPosition(position.x, position.y);

      // If the panel has already been created, add the new origin class to
      // the panel element and update it's position with the panelPosition.
      if (panelRef && panelRef.panelEl) {
        panelRef.panelEl.addClass(origin);
        panelRef.updatePosition(panelPosition);
      }
    }

    function bindEvents() {
      // Add a mutationObserver where there is support for it and the need
      // for it in the form of viable host(parent[0]).
      if (parent[0] && 'MutationObserver' in $window) {
        // Use a mutationObserver to tackle #2602.
        var attributeObserver = new MutationObserver(function(mutations) {
          if (isDisabledMutation(mutations)) {
            $mdUtil.nextTick(function() {
              setVisible(false);
            });
          }
        });

        attributeObserver.observe(parent[0], {
          attributes: true
        });
      }

      elementFocusedOnWindowBlur = false;

      $$mdTooltipRegistry.register('scroll', windowScrollEventHandler, true);
      $$mdTooltipRegistry.register('blur', windowBlurEventHandler);
      $$mdTooltipRegistry.register('resize', debouncedOnResize);

      scope.$on('$destroy', onDestroy);

      // To avoid 'synthetic clicks', we listen to mousedown instead of
      // 'click'.
      parent.on('mousedown', mousedownEventHandler);
      parent.on(ENTER_EVENTS, enterEventHandler);

      function isDisabledMutation(mutations) {
        mutations.some(function(mutation) {
          return mutation.attributeName === 'disabled' && parent[0].disabled;
        });
        return false;
      }

      function windowScrollEventHandler() {
        setVisible(false);
      }

      function windowBlurEventHandler() {
        elementFocusedOnWindowBlur = document.activeElement === parent[0];
      }

      function enterEventHandler($event) {
        // Prevent the tooltip from showing when the window is receiving
        // focus.
        if ($event.type === 'focus' && elementFocusedOnWindowBlur) {
          elementFocusedOnWindowBlur = false;
        } else if (!scope.mdVisible) {
          parent.on(LEAVE_EVENTS, leaveEventHandler);
          setVisible(true);

          // If the user is on a touch device, we should bind the tap away
          // after the 'touched' in order to prevent the tooltip being
          // removed immediately.
          if ($event.type === 'touchstart') {
            parent.one('touchend', function() {
              $mdUtil.nextTick(function() {
                $document.one('touchend', leaveEventHandler);
              }, false);
            });
          }
        }
      }

      function leaveEventHandler() {
        autohide = scope.hasOwnProperty('mdAutohide') ?
            scope.mdAutohide :
            attr.hasOwnProperty('mdAutohide');

        if (autohide || mouseActive ||
            $document[0].activeElement !== parent[0]) {
          // When a show timeout is currently in progress, then we have
          // to cancel it, otherwise the tooltip will remain showing
          // without focus or hover.
          if (showTimeout) {
            $timeout.cancel(showTimeout);
            setVisible.queued = false;
            showTimeout = null;
          }

          parent.off(LEAVE_EVENTS, leaveEventHandler);
          parent.triggerHandler('blur');
          setVisible(false);
        }
        mouseActive = false;
      }

      function mousedownEventHandler() {
        mouseActive = true;
      }

      function onDestroy() {
        $$mdTooltipRegistry.deregister('scroll', windowScrollEventHandler, true);
        $$mdTooltipRegistry.deregister('blur', windowBlurEventHandler);
        $$mdTooltipRegistry.deregister('resize', debouncedOnResize);

        parent
            .off(ENTER_EVENTS, enterEventHandler)
            .off(LEAVE_EVENTS, leaveEventHandler)
            .off('mousedown', mousedownEventHandler);

        // Trigger the handler in case any of the tooltips are
        // still visible.
        leaveEventHandler();
        attributeObserver && attributeObserver.disconnect();
      }
    }

    function configureWatchers() {
      if (element[0] && 'MutationObserver' in $window) {
        var attributeObserver = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.attributeName === 'md-visible' &&
                !scope.visibleWatcher ) {
              scope.visibleWatcher = scope.$watch('mdVisible',
                  onVisibleChanged);
            }
          });
        });

        attributeObserver.observe(element[0], {
          attributes: true
        });

        // Build watcher only if mdVisible is being used.
        if (attr.hasOwnProperty('mdVisible')) {
          scope.visibleWatcher = scope.$watch('mdVisible',
              onVisibleChanged);
        }
      } else {
        // MutationObserver not supported
        scope.visibleWatcher = scope.$watch('mdVisible', onVisibleChanged);
      }

      // Direction watcher
      scope.$watch('mdDirection', updatePosition);

      // Clean up if the element or parent was removed via jqLite's .remove.
      // A couple of notes:
      //   - In these cases the scope might not have been destroyed, which
      //     is why we destroy it manually. An example of this can be having
      //     `md-visible="false"` and adding tooltips while they're
      //     invisible. If `md-visible` becomes true, at some point, you'd
      //     usually get a lot of tooltips.
      //   - We use `.one`, not `.on`, because this only needs to fire once.
      //     If we were using `.on`, it would get thrown into an infinite
      //     loop.
      //   - This kicks off the scope's `$destroy` event which finishes the
      //     cleanup.
      element.one('$destroy', onElementDestroy);
      parent.one('$destroy', onElementDestroy);
      scope.$on('$destroy', function() {
        setVisible(false);
        panelRef && panelRef.destroy();
        attributeObserver && attributeObserver.disconnect();
        element.remove();
      });

      // Updates the aria-label when the element text changes. This watch
      // doesn't need to be set up if the element doesn't have any data
      // bindings.
      if (element.text().indexOf($interpolate.startSymbol()) > -1) {
        scope.$watch(function() {
          return element.text().trim();
        }, addAriaLabel);
      }

      function onElementDestroy() {
        scope.$destroy();
      }
    }

    function setVisible(value) {
      // Break if passed value is already in queue or there is no queue and
      // passed value is current in the controller.
      if (setVisible.queued && setVisible.value === !!value ||
          !setVisible.queued && scope.mdVisible === !!value) {
        return;
      }
      setVisible.value = !!value;

      if (!setVisible.queued) {
        if (value) {
          setVisible.queued = true;
          showTimeout = $timeout(function() {
            scope.mdVisible = setVisible.value;
            setVisible.queued = false;
            showTimeout = null;
            if (!scope.visibleWatcher) {
              onVisibleChanged(scope.mdVisible);
            }
          }, scope.mdDelay);
        } else {
          $mdUtil.nextTick(function() {
            scope.mdVisible = false;
            if (!scope.visibleWatcher) {
              onVisibleChanged(false);
            }
          });
        }
      }
    }

    function onVisibleChanged(isVisible) {
      isVisible ? showTooltip() : hideTooltip();
    }

    function showTooltip() {
      // Do not show the tooltip if the text is empty.
      if (!element[0].textContent.trim()) {
        throw new Error('Text for the tooltip has not been provided. ' +
            'Please include text within the mdTooltip element.');
      }

      if (!panelRef) {
        var attachTo = angular.element(document.body);
        var panelAnimation = $mdPanel.newPanelAnimation()
            .openFrom(parent)
            .closeTo(parent)
            .withAnimation({
              open: 'md-show',
              close: 'md-hide'
            });

        var panelConfig = {
          id: tooltipId,
          attachTo: attachTo,
          contentElement: element,
          propagateContainerEvents: true,
          panelClass: 'md-tooltip ' + origin,
          animation: panelAnimation,
          position: panelPosition,
          zIndex: scope.mdZIndex,
          focusOnOpen: false
        };

        panelRef = $mdPanel.create(panelConfig);
      }

      panelRef.open().then(function() {
        panelRef.panelEl.attr('role', 'tooltip');
      });
    }

    function hideTooltip() {
      panelRef && panelRef.close();
    }
  }

}


/**
 * Service that is used to reduce the amount of listeners that are being
 * registered on the `window` by the tooltip component. Works by collecting
 * the individual event handlers and dispatching them from a global handler.
 *
 * @ngInject
 */
function MdTooltipRegistry() {
  var listeners = {};
  var ngWindow = angular.element(window);

  return {
    register: register,
    deregister: deregister
  };

  /**
   * Global event handler that dispatches the registered handlers in the
   * service.
   * @param {!Event} event Event object passed in by the browser
   */
  function globalEventHandler(event) {
    if (listeners[event.type]) {
      listeners[event.type].forEach(function(currentHandler) {
        currentHandler.call(this, event);
      }, this);
    }
  }

  /**
   * Registers a new handler with the service.
   * @param {string} type Type of event to be registered.
   * @param {!Function} handler Event handler.
   * @param {boolean} useCapture Whether to use event capturing.
   */
  function register(type, handler, useCapture) {
    var handlers = listeners[type] = listeners[type] || [];

    if (!handlers.length) {
      useCapture ? window.addEventListener(type, globalEventHandler, true) :
          ngWindow.on(type, globalEventHandler);
    }

    if (handlers.indexOf(handler) === -1) {
      handlers.push(handler);
    }
  }

  /**
   * Removes an event handler from the service.
   * @param {string} type Type of event handler.
   * @param {!Function} handler The event handler itself.
   * @param {boolean} useCapture Whether the event handler used event capturing.
   */
  function deregister(type, handler, useCapture) {
    var handlers = listeners[type];
    var index = handlers ? handlers.indexOf(handler) : -1;

    if (index > -1) {
      handlers.splice(index, 1);

      if (handlers.length === 0) {
        useCapture ? window.removeEventListener(type, globalEventHandler, true) :
            ngWindow.off(type, globalEventHandler);
      }
    }
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.truncate
 */
MdTruncateController.$inject = ["$element"];
angular.module('material.components.truncate', ['material.core'])
  .directive('mdTruncate', MdTruncateDirective);

/**
 * @ngdoc directive
 * @name mdTruncate
 * @module material.components.truncate
 * @restrict AE
 * @description
 *
 * The `md-truncate` component displays a label that will automatically clip text which is wider
 * than the component. By default, it displays an ellipsis, but you may apply the `md-clip` CSS
 * class to override this default and use a standard "clipping" approach.
 *
 * <i><b>Note:</b> The `md-truncate` component does not automatically adjust it's width. You must
 * provide the `flex` attribute, or some other CSS-based width management. See the
 * <a ng-href="./demo/truncate">demos</a> for examples.</i>
 *
 * @usage
 *
 * ### As an Element
 *
 * <hljs lang="html">
 *   <div layout="row">
 *     <md-button>Back</md-button>
 *
 *     <md-truncate flex>Chapter 1 - The Way of the Old West</md-truncate>
 *
 *     <md-button>Forward</md-button>
 *   </div>
 * </hljs>
 *
 * ### As an Attribute
 *
 * <hljs lang="html">
 *   <h2 md-truncate style="max-width: 100px;">Some Title With a Lot of Text</h2>
 * </hljs>
 *
 * ## CSS & Styles
 *
 * `<md-truncate>` provides two CSS classes that you may use to control the type of clipping.
 *
 * <i><b>Note:</b> The `md-truncate` also applies a setting of `width: 0;` when used with the `flex`
 * attribute to fix an issue with the flex element not shrinking properly.</i>
 *
 * <div>
 * <docs-css-api-table>
 *
 *   <docs-css-selector code=".md-ellipsis">
 *     Assigns the "ellipsis" behavior (default) which will cut off mid-word and append an ellipsis
 *     (&hellip;) to the end of the text.
 *   </docs-css-selector>
 *
 *   <docs-css-selector code=".md-clip">
 *     Assigns the "clipping" behavior which will simply chop off the text. This may happen
 *     mid-word or even mid-character.
 *   </docs-css-selector>
 *
 * </docs-css-api-table>
 * </div>
 */
function MdTruncateDirective() {
  return {
    restrict: 'AE',

    controller: MdTruncateController,
    controllerAs: '$ctrl',
    bindToController: true
  }
}

/**
 * Controller for the <md-truncate> component.
 *
 * @param $element The md-truncate element.
 *
 * @constructor
 * @ngInject
 */
function MdTruncateController($element) {
  $element.addClass('md-truncate');
}

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.virtualRepeat
 */
VirtualRepeatContainerController.$inject = ["$$rAF", "$mdUtil", "$mdConstant", "$parse", "$rootScope", "$window", "$scope", "$element", "$attrs"];
VirtualRepeatController.$inject = ["$scope", "$element", "$attrs", "$browser", "$document", "$rootScope", "$$rAF", "$mdUtil"];
VirtualRepeatDirective.$inject = ["$parse"];
angular.module('material.components.virtualRepeat', [
  'material.core',
  'material.components.showHide'
])
.directive('mdVirtualRepeatContainer', VirtualRepeatContainerDirective)
.directive('mdVirtualRepeat', VirtualRepeatDirective)
.directive('mdForceHeight', ForceHeightDirective);


/**
 * @ngdoc directive
 * @name mdVirtualRepeatContainer
 * @module material.components.virtualRepeat
 * @restrict E
 * @description
 * `md-virtual-repeat-container` provides the scroll container for md-virtual-repeat.
 *
 * VirtualRepeat is a limited substitute for ng-repeat that renders only
 * enough DOM nodes to fill the container and recycling them as the user scrolls.
 *
 * Once an element is not visible anymore, the VirtualRepeat recycles it and will reuse it for
 * another visible item by replacing the previous dataset with the new one.
 *
 * ### Common Issues
 *
 * - When having one-time bindings inside of the view template, the VirtualRepeat will not properly
 *   update the bindings for new items, since the view will be recycled.
 *
 * - Directives inside of a VirtualRepeat will be only compiled (linked) once, because those
 *   are will be recycled items and used for other items.
 *   The VirtualRepeat just updates the scope bindings.
 *
 *
 * ### Notes
 *
 * > The VirtualRepeat is a similar implementation to the Android
 * [RecyclerView](https://developer.android.com/reference/android/support/v7/widget/RecyclerView.html)
 *
 * <!-- This comment forces a break between blockquotes //-->
 *
 * > Please also review the <a ng-href="api/directive/mdVirtualRepeat">VirtualRepeat</a>
 * documentation for more information.
 *
 *
 * @usage
 * <hljs lang="html">
 *
 * <md-virtual-repeat-container md-top-index="topIndex">
 *   <div md-virtual-repeat="i in items" md-item-size="20">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 * </hljs>
 *
 * @param {number=} md-top-index Binds the index of the item that is at the top of the scroll
 *     container to $scope. It can both read and set the scroll position.
 * @param {boolean=} md-orient-horizontal Whether the container should scroll horizontally
 *     (defaults to orientation and scrolling vertically).
 * @param {boolean=} md-auto-shrink When present, the container will shrink to fit
 *     the number of items when that number is less than its original size.
 * @param {number=} md-auto-shrink-min Minimum number of items that md-auto-shrink
 *     will shrink to (default: 0).
 */
function VirtualRepeatContainerDirective() {
  return {
    controller: VirtualRepeatContainerController,
    template: virtualRepeatContainerTemplate,
    compile: function virtualRepeatContainerCompile($element, $attrs) {
      $element
          .addClass('md-virtual-repeat-container')
          .addClass($attrs.hasOwnProperty('mdOrientHorizontal')
              ? 'md-orient-horizontal'
              : 'md-orient-vertical');
    }
  };
}


function virtualRepeatContainerTemplate($element) {
  return '<div class="md-virtual-repeat-scroller" role="presentation">' +
    '<div class="md-virtual-repeat-sizer" role="presentation"></div>' +
    '<div class="md-virtual-repeat-offsetter" role="presentation">' +
      $element[0].innerHTML +
    '</div></div>';
}

/**
 * Number of additional elements to render above and below the visible area inside
 * of the virtual repeat container. A higher number results in less flicker when scrolling
 * very quickly in Safari, but comes with a higher rendering and dirty-checking cost.
 * @const {number}
 */
var NUM_EXTRA = 3;

/** @ngInject */
function VirtualRepeatContainerController($$rAF, $mdUtil, $mdConstant, $parse, $rootScope, $window, $scope,
                                          $element, $attrs) {
  this.$rootScope = $rootScope;
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;

  /** @type {number} The width or height of the container */
  this.size = 0;
  /** @type {number} The scroll width or height of the scroller */
  this.scrollSize = 0;
  /** @type {number} The scrollLeft or scrollTop of the scroller */
  this.scrollOffset = 0;
  /** @type {boolean} Whether the scroller is oriented horizontally */
  this.horizontal = this.$attrs.hasOwnProperty('mdOrientHorizontal');
  /** @type {!VirtualRepeatController} The repeater inside of this container */
  this.repeater = null;
  /** @type {boolean} Whether auto-shrink is enabled */
  this.autoShrink = this.$attrs.hasOwnProperty('mdAutoShrink');
  /** @type {number} Minimum number of items to auto-shrink to */
  this.autoShrinkMin = parseInt(this.$attrs.mdAutoShrinkMin, 10) || 0;
  /** @type {?number} Original container size when shrank */
  this.originalSize = null;
  /** @type {number} Amount to offset the total scroll size by. */
  this.offsetSize = parseInt(this.$attrs.mdOffsetSize, 10) || 0;
  /** @type {?string} height or width element style on the container prior to auto-shrinking. */
  this.oldElementSize = null;
  /** @type {!number} Maximum amount of pixels allowed for a single DOM element */
  this.maxElementPixels = $mdConstant.ELEMENT_MAX_PIXELS;

  if (this.$attrs.mdTopIndex) {
    /** @type {function(angular.Scope): number} Binds to topIndex on AngularJS scope */
    this.bindTopIndex = $parse(this.$attrs.mdTopIndex);
    /** @type {number} The index of the item that is at the top of the scroll container */
    this.topIndex = this.bindTopIndex(this.$scope);

    if (!angular.isDefined(this.topIndex)) {
      this.topIndex = 0;
      this.bindTopIndex.assign(this.$scope, 0);
    }

    this.$scope.$watch(this.bindTopIndex, angular.bind(this, function(newIndex) {
      if (newIndex !== this.topIndex) {
        this.scrollToIndex(newIndex);
      }
    }));
  } else {
    this.topIndex = 0;
  }

  this.scroller = $element[0].querySelector('.md-virtual-repeat-scroller');
  this.sizer = this.scroller.querySelector('.md-virtual-repeat-sizer');
  this.offsetter = this.scroller.querySelector('.md-virtual-repeat-offsetter');

  // After the dom stablizes, measure the initial size of the container and
  // make a best effort at re-measuring as it changes.
  var boundUpdateSize = angular.bind(this, this.updateSize);

  $$rAF(angular.bind(this, function() {
    boundUpdateSize();

    var debouncedUpdateSize = $mdUtil.debounce(boundUpdateSize, 10, null, false);
    var jWindow = angular.element($window);

    // Make one more attempt to get the size if it is 0.
    // This is not by any means a perfect approach, but there's really no
    // silver bullet here.
    if (!this.size) {
      debouncedUpdateSize();
    }

    jWindow.on('resize', debouncedUpdateSize);
    $scope.$on('$destroy', function() {
      jWindow.off('resize', debouncedUpdateSize);
    });

    $scope.$emit('$md-resize-enable');
    $scope.$on('$md-resize', boundUpdateSize);
  }));
}


/** Called by the md-virtual-repeat inside of the container at startup. */
VirtualRepeatContainerController.prototype.register = function(repeaterCtrl) {
  this.repeater = repeaterCtrl;

  angular.element(this.scroller)
      .on('scroll wheel touchmove touchend', angular.bind(this, this.handleScroll_));
};


/** @return {boolean} Whether the container is configured for horizontal scrolling. */
VirtualRepeatContainerController.prototype.isHorizontal = function() {
  return this.horizontal;
};


/** @return {number} The size (width or height) of the container. */
VirtualRepeatContainerController.prototype.getSize = function() {
  return this.size;
};


/**
 * Resizes the container.
 * @private
 * @param {number} size The new size to set.
 */
VirtualRepeatContainerController.prototype.setSize_ = function(size) {
  var dimension = this.getDimensionName_();

  this.size = size;
  this.$element[0].style[dimension] = size + 'px';
};


VirtualRepeatContainerController.prototype.unsetSize_ = function() {
  this.$element[0].style[this.getDimensionName_()] = this.oldElementSize;
  this.oldElementSize = null;
};


/** Instructs the container to re-measure its size. */
VirtualRepeatContainerController.prototype.updateSize = function() {
  // If the original size is already determined, we can skip the update.
  if (this.originalSize) return;

  this.size = this.isHorizontal()
      ? this.$element[0].clientWidth
      : this.$element[0].clientHeight;

  // Recheck the scroll position after updating the size. This resolves
  // problems that can result if the scroll position was measured while the
  // element was display: none or detached from the document.
  this.handleScroll_();

  this.repeater && this.repeater.containerUpdated();
};


/** @return {number} The container's scrollHeight or scrollWidth. */
VirtualRepeatContainerController.prototype.getScrollSize = function() {
  return this.scrollSize;
};


VirtualRepeatContainerController.prototype.getDimensionName_ = function() {
  return this.isHorizontal() ? 'width' : 'height';
};


/**
 * Sets the scroller element to the specified size.
 * @private
 * @param {number} size The new size.
 */
VirtualRepeatContainerController.prototype.sizeScroller_ = function(size) {
  var dimension =  this.getDimensionName_();
  var crossDimension = this.isHorizontal() ? 'height' : 'width';

  // Clear any existing dimensions.
  this.sizer.innerHTML = '';

  // If the size falls within the browser's maximum explicit size for a single element, we can
  // set the size and be done. Otherwise, we have to create children that add up the the desired
  // size.
  if (size < this.maxElementPixels) {
    this.sizer.style[dimension] = size + 'px';
  } else {
    this.sizer.style[dimension] = 'auto';
    this.sizer.style[crossDimension] = 'auto';

    // Divide the total size we have to render into N max-size pieces.
    var numChildren = Math.floor(size / this.maxElementPixels);

    // Element template to clone for each max-size piece.
    var sizerChild = document.createElement('div');
    sizerChild.style[dimension] = this.maxElementPixels + 'px';
    sizerChild.style[crossDimension] = '1px';

    for (var i = 0; i < numChildren; i++) {
      this.sizer.appendChild(sizerChild.cloneNode(false));
    }

    // Re-use the element template for the remainder.
    sizerChild.style[dimension] = (size - (numChildren * this.maxElementPixels)) + 'px';
    this.sizer.appendChild(sizerChild);
  }
};


/**
 * If auto-shrinking is enabled, shrinks or unshrinks as appropriate.
 * @private
 * @param {number} size The new size.
 */
VirtualRepeatContainerController.prototype.autoShrink_ = function(size) {
  var shrinkSize = Math.max(size, this.autoShrinkMin * this.repeater.getItemSize());

  if (this.autoShrink && shrinkSize !== this.size) {
    if (this.oldElementSize === null) {
      this.oldElementSize = this.$element[0].style[this.getDimensionName_()];
    }

    var currentSize = this.originalSize || this.size;

    if (!currentSize || shrinkSize < currentSize) {
      if (!this.originalSize) {
        this.originalSize = this.size;
      }

      // Now we update the containers size, because shrinking is enabled.
      this.setSize_(shrinkSize);
    } else if (this.originalSize !== null) {
      // Set the size back to our initial size.
      this.unsetSize_();

      var _originalSize = this.originalSize;
      this.originalSize = null;

      // We determine the repeaters size again, if the original size was zero.
      // The originalSize needs to be null, to be able to determine the size.
      if (!_originalSize) this.updateSize();

      // Apply the original size or the determined size back to the container, because
      // it has been overwritten before, in the shrink block.
      this.setSize_(_originalSize || this.size);
    }

    this.repeater.containerUpdated();
  }
};


/**
 * Sets the scrollHeight or scrollWidth. Called by the repeater based on
 * its item count and item size.
 * @param {number} itemsSize The total size of the items.
 */
VirtualRepeatContainerController.prototype.setScrollSize = function(itemsSize) {
  var size = itemsSize + this.offsetSize;
  if (this.scrollSize === size) return;

  this.sizeScroller_(size);
  this.autoShrink_(size);
  this.scrollSize = size;
};


/** @return {number} The container's current scroll offset. */
VirtualRepeatContainerController.prototype.getScrollOffset = function() {
  return this.scrollOffset;
};

/**
 * Scrolls to a given scrollTop position.
 * @param {number} position
 */
VirtualRepeatContainerController.prototype.scrollTo = function(position) {
  this.scroller[this.isHorizontal() ? 'scrollLeft' : 'scrollTop'] = position;
  this.handleScroll_();
};

/**
 * Scrolls the item with the given index to the top of the scroll container.
 * @param {number} index
 */
VirtualRepeatContainerController.prototype.scrollToIndex = function(index) {
  var itemSize = this.repeater.getItemSize();
  var itemsLength = this.repeater.itemsLength;
  if(index > itemsLength) {
    index = itemsLength - 1;
  }
  this.scrollTo(itemSize * index);
};

VirtualRepeatContainerController.prototype.resetScroll = function() {
  this.scrollTo(0);
};


VirtualRepeatContainerController.prototype.handleScroll_ = function() {
  var ltr = document.dir != 'rtl' && document.body.dir != 'rtl';
  if(!ltr && !this.maxSize) {
    this.scroller.scrollLeft = this.scrollSize;
    this.maxSize = this.scroller.scrollLeft;
  }
  var offset = this.isHorizontal() ?
      (ltr?this.scroller.scrollLeft : this.maxSize - this.scroller.scrollLeft)
      : this.scroller.scrollTop;
  if (offset === this.scrollOffset || offset > this.scrollSize - this.size) return;

  var itemSize = this.repeater.getItemSize();
  if (!itemSize) return;

  var numItems = Math.max(0, Math.floor(offset / itemSize) - NUM_EXTRA);

  var transform = (this.isHorizontal() ? 'translateX(' : 'translateY(') +
      (!this.isHorizontal() || ltr ? (numItems * itemSize) : - (numItems * itemSize))  + 'px)';

  this.scrollOffset = offset;
  this.offsetter.style.webkitTransform = transform;
  this.offsetter.style.transform = transform;

  if (this.bindTopIndex) {
    var topIndex = Math.floor(offset / itemSize);
    if (topIndex !== this.topIndex && topIndex < this.repeater.getItemCount()) {
      this.topIndex = topIndex;
      this.bindTopIndex.assign(this.$scope, topIndex);
      if (!this.$rootScope.$$phase) this.$scope.$digest();
    }
  }

  this.repeater.containerUpdated();
};


/**
 * @ngdoc directive
 * @name mdVirtualRepeat
 * @module material.components.virtualRepeat
 * @restrict A
 * @priority 1000
 * @description
 * `md-virtual-repeat` specifies an element to repeat using virtual scrolling.
 *
 * Virtual repeat is a limited substitute for ng-repeat that renders only
 * enough DOM nodes to fill the container and recycling them as the user scrolls.
 *
 * Arrays, but not objects are supported for iteration.
 * Track by, as alias, and (key, value) syntax are not supported.
 *
 * ### On-Demand Async Item Loading
 *
 * When using the `md-on-demand` attribute and loading some asynchronous data, the `getItemAtIndex` function will
 * mostly return nothing.
 *
 * <hljs lang="js">
 *   DynamicItems.prototype.getItemAtIndex = function(index) {
 *     if (this.pages[index]) {
 *       return this.pages[index];
 *     } else {
 *       // This is an asynchronous action and does not return any value.
 *       this.loadPage(index);
 *     }
 *   };
 * </hljs>
 *
 * This means that the VirtualRepeat will not have any value for the given index.<br/>
 * After the data loading completed, the user expects the VirtualRepeat to recognize the change.
 *
 * To make sure that the VirtualRepeat properly detects any change, you need to run the operation
 * in another digest.
 *
 * <hljs lang="js">
 *   DynamicItems.prototype.loadPage = function(index) {
 *     var self = this;
 *
 *     // Trigger a new digest by using $timeout
 *     $timeout(function() {
 *       self.pages[index] = Data;
 *     });
 *   };
 * </hljs>
 *
 * > <b>Note:</b> Please also review the
 *   <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> documentation
 *   for more information.
 *
 * @usage
 * <hljs lang="html">
 * <md-virtual-repeat-container>
 *   <div md-virtual-repeat="i in items">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 *
 * <md-virtual-repeat-container md-orient-horizontal>
 *   <div md-virtual-repeat="i in items" md-item-size="20">Hello {{i}}!</div>
 * </md-virtual-repeat-container>
 * </hljs>
 *
 * @param {number=} md-item-size The height or width of the repeated elements (which must be
 *   identical for each element). Optional. Will attempt to read the size from the dom if missing,
 *   but still assumes that all repeated nodes have same height or width.
 * @param {string=} md-extra-name Evaluates to an additional name to which the current iterated item
 *   can be assigned on the repeated scope (needed for use in `md-autocomplete`).
 * @param {boolean=} md-on-demand When present, treats the md-virtual-repeat argument as an object
 *   that can fetch rows rather than an array.
 *
 *   **NOTE:** This object must implement the following interface with two (2) methods:
 *
 *   - `getItemAtIndex: function(index) [object]` The item at that index or null if it is not yet
 *     loaded (it should start downloading the item in that case).
 *   - `getLength: function() [number]` The data length to which the repeater container
 *     should be sized. Ideally, when the count is known, this method should return it.
 *     Otherwise, return a higher number than the currently loaded items to produce an
 *     infinite-scroll behavior.
 */
function VirtualRepeatDirective($parse) {
  return {
    controller: VirtualRepeatController,
    priority: 1000,
    require: ['mdVirtualRepeat', '^^mdVirtualRepeatContainer'],
    restrict: 'A',
    terminal: true,
    transclude: 'element',
    compile: function VirtualRepeatCompile($element, $attrs) {
      var expression = $attrs.mdVirtualRepeat;
      var match = expression.match(/^\s*([\s\S]+?)\s+in\s+([\s\S]+?)\s*$/);
      var repeatName = match[1];
      var repeatListExpression = $parse(match[2]);
      var extraName = $attrs.mdExtraName && $parse($attrs.mdExtraName);

      return function VirtualRepeatLink($scope, $element, $attrs, ctrl, $transclude) {
        ctrl[0].link_(ctrl[1], $transclude, repeatName, repeatListExpression, extraName);
      };
    }
  };
}


/** @ngInject */
function VirtualRepeatController($scope, $element, $attrs, $browser, $document, $rootScope,
    $$rAF, $mdUtil) {
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;
  this.$browser = $browser;
  this.$document = $document;
  this.$mdUtil = $mdUtil;
  this.$rootScope = $rootScope;
  this.$$rAF = $$rAF;

  /** @type {boolean} Whether we are in on-demand mode. */
  this.onDemand = $mdUtil.parseAttributeBoolean($attrs.mdOnDemand);
  /** @type {!Function} Backup reference to $browser.$$checkUrlChange */
  this.browserCheckUrlChange = $browser.$$checkUrlChange;
  /** @type {number} Most recent starting repeat index (based on scroll offset) */
  this.newStartIndex = 0;
  /** @type {number} Most recent ending repeat index (based on scroll offset) */
  this.newEndIndex = 0;
  /** @type {number} Most recent end visible index (based on scroll offset) */
  this.newVisibleEnd = 0;
  /** @type {number} Previous starting repeat index (based on scroll offset) */
  this.startIndex = 0;
  /** @type {number} Previous ending repeat index (based on scroll offset) */
  this.endIndex = 0;
  // TODO: measure width/height of first element from dom if not provided.
  // getComputedStyle?
  /** @type {?number} Height/width of repeated elements. */
  this.itemSize = $scope.$eval($attrs.mdItemSize) || null;

  /** @type {boolean} Whether this is the first time that items are rendered. */
  this.isFirstRender = true;

  /**
   * @private {boolean} Whether the items in the list are already being updated. Used to prevent
   *     nested calls to virtualRepeatUpdate_.
   */
  this.isVirtualRepeatUpdating_ = false;

  /** @type {number} Most recently seen length of items. */
  this.itemsLength = 0;

  /**
   * @type {!Function} Unwatch callback for item size (when md-items-size is
   *     not specified), or angular.noop otherwise.
   */
  this.unwatchItemSize_ = angular.noop;

  /**
   * Presently rendered blocks by repeat index.
   * @type {Object<number, !VirtualRepeatController.Block}
   */
  this.blocks = {};
  /** @type {Array<!VirtualRepeatController.Block>} A pool of presently unused blocks. */
  this.pooledBlocks = [];

  $scope.$on('$destroy', angular.bind(this, this.cleanupBlocks_));
}


/**
 * An object representing a repeated item.
 * @typedef {{element: !jqLite, new: boolean, scope: !angular.Scope}}
 */
VirtualRepeatController.Block;


/**
 * Called at startup by the md-virtual-repeat postLink function.
 * @param {!VirtualRepeatContainerController} container The container's controller.
 * @param {!Function} transclude The repeated element's bound transclude function.
 * @param {string} repeatName The left hand side of the repeat expression, indicating
 *     the name for each item in the array.
 * @param {!Function} repeatListExpression A compiled expression based on the right hand side
 *     of the repeat expression. Points to the array to repeat over.
 * @param {string|undefined} extraName The optional extra repeatName.
 */
VirtualRepeatController.prototype.link_ =
    function(container, transclude, repeatName, repeatListExpression, extraName) {
  this.container = container;
  this.transclude = transclude;
  this.repeatName = repeatName;
  this.rawRepeatListExpression = repeatListExpression;
  this.extraName = extraName;
  this.sized = false;

  this.repeatListExpression = angular.bind(this, this.repeatListExpression_);

  this.container.register(this);
};


/** @private Cleans up unused blocks. */
VirtualRepeatController.prototype.cleanupBlocks_ = function() {
  angular.forEach(this.pooledBlocks, function cleanupBlock(block) {
    block.element.remove();
  });
};


/** @private Attempts to set itemSize by measuring a repeated element in the dom */
VirtualRepeatController.prototype.readItemSize_ = function() {
  if (this.itemSize) {
    // itemSize was successfully read in a different asynchronous call.
    return;
  }

  this.items = this.repeatListExpression(this.$scope);
  this.parentNode = this.$element[0].parentNode;
  var block = this.getBlock_(0);
  if (!block.element[0].parentNode) {
    this.parentNode.appendChild(block.element[0]);
  }

  this.itemSize = block.element[0][
      this.container.isHorizontal() ? 'offsetWidth' : 'offsetHeight'] || null;

  this.blocks[0] = block;
  this.poolBlock_(0);

  if (this.itemSize) {
    this.containerUpdated();
  }
};


/**
 * Returns the user-specified repeat list, transforming it into an array-like
 * object in the case of infinite scroll/dynamic load mode.
 * @param {!angular.Scope} The scope.
 * @return {!Array|!Object} An array or array-like object for iteration.
 */
VirtualRepeatController.prototype.repeatListExpression_ = function(scope) {
  var repeatList = this.rawRepeatListExpression(scope);

  if (this.onDemand && repeatList) {
    var virtualList = new VirtualRepeatModelArrayLike(repeatList);
    virtualList.$$includeIndexes(this.newStartIndex, this.newVisibleEnd);
    return virtualList;
  } else {
    return repeatList;
  }
};


/**
 * Called by the container. Informs us that the containers scroll or size has
 * changed.
 */
VirtualRepeatController.prototype.containerUpdated = function() {
  // If itemSize is unknown, attempt to measure it.
  if (!this.itemSize) {
    // Make sure to clean up watchers if we can (see #8178)
    if(this.unwatchItemSize_ && this.unwatchItemSize_ !== angular.noop){
      this.unwatchItemSize_();
    }
    this.unwatchItemSize_ = this.$scope.$watchCollection(
        this.repeatListExpression,
        angular.bind(this, function(items) {
          if (items && items.length) {
            this.readItemSize_();
          }
        }));
    if (!this.$rootScope.$$phase) this.$scope.$digest();

    return;
  } else if (!this.sized) {
    this.items = this.repeatListExpression(this.$scope);
  }

  if (!this.sized) {
    this.unwatchItemSize_();
    this.sized = true;
    this.$scope.$watchCollection(this.repeatListExpression,
        angular.bind(this, function(items, oldItems) {
          if (!this.isVirtualRepeatUpdating_) {
            this.virtualRepeatUpdate_(items, oldItems);
          }
        }));
  }

  this.updateIndexes_();

  if (this.newStartIndex !== this.startIndex ||
      this.newEndIndex !== this.endIndex ||
      this.container.getScrollOffset() > this.container.getScrollSize()) {
    if (this.items instanceof VirtualRepeatModelArrayLike) {
      this.items.$$includeIndexes(this.newStartIndex, this.newEndIndex);
    }
    this.virtualRepeatUpdate_(this.items, this.items);
  }
};


/**
 * Called by the container. Returns the size of a single repeated item.
 * @return {?number} Size of a repeated item.
 */
VirtualRepeatController.prototype.getItemSize = function() {
  return this.itemSize;
};


/**
 * Called by the container. Returns the size of a single repeated item.
 * @return {?number} Size of a repeated item.
 */
VirtualRepeatController.prototype.getItemCount = function() {
  return this.itemsLength;
};


/**
 * Updates the order and visible offset of repeated blocks in response to scrolling
 * or items updates.
 * @private
 */
VirtualRepeatController.prototype.virtualRepeatUpdate_ = function(items, oldItems) {
  this.isVirtualRepeatUpdating_ = true;

  var itemsLength = items && items.length || 0;
  var lengthChanged = false;

  // If the number of items shrank, keep the scroll position.
  if (this.items && itemsLength < this.items.length && this.container.getScrollOffset() !== 0) {
    this.items = items;
    var previousScrollOffset = this.container.getScrollOffset();
    this.container.resetScroll();
    this.container.scrollTo(previousScrollOffset);
  }

  if (itemsLength !== this.itemsLength) {
    lengthChanged = true;
    this.itemsLength = itemsLength;
  }

  this.items = items;
  if (items !== oldItems || lengthChanged) {
    this.updateIndexes_();
  }

  this.parentNode = this.$element[0].parentNode;

  if (lengthChanged) {
    this.container.setScrollSize(itemsLength * this.itemSize);
  }

  // Detach and pool any blocks that are no longer in the viewport.
  Object.keys(this.blocks).forEach(function(blockIndex) {
    var index = parseInt(blockIndex, 10);
    if (index < this.newStartIndex || index >= this.newEndIndex) {
      this.poolBlock_(index);
    }
  }, this);

  // Add needed blocks.
  // For performance reasons, temporarily block browser url checks as we digest
  // the restored block scopes ($$checkUrlChange reads window.location to
  // check for changes and trigger route change, etc, which we don't need when
  // trying to scroll at 60fps).
  this.$browser.$$checkUrlChange = angular.noop;

  var i, block,
      newStartBlocks = [],
      newEndBlocks = [];

  // Collect blocks at the top.
  for (i = this.newStartIndex; i < this.newEndIndex && this.blocks[i] == null; i++) {
    block = this.getBlock_(i);
    this.updateBlock_(block, i);
    newStartBlocks.push(block);
  }

  // Update blocks that are already rendered.
  for (; this.blocks[i] != null; i++) {
    this.updateBlock_(this.blocks[i], i);
  }
  var maxIndex = i - 1;

  // Collect blocks at the end.
  for (; i < this.newEndIndex; i++) {
    block = this.getBlock_(i);
    this.updateBlock_(block, i);
    newEndBlocks.push(block);
  }

  // Attach collected blocks to the document.
  if (newStartBlocks.length) {
    this.parentNode.insertBefore(
        this.domFragmentFromBlocks_(newStartBlocks),
        this.$element[0].nextSibling);
  }
  if (newEndBlocks.length) {
    this.parentNode.insertBefore(
        this.domFragmentFromBlocks_(newEndBlocks),
        this.blocks[maxIndex] && this.blocks[maxIndex].element[0].nextSibling);
  }

  // Restore $$checkUrlChange.
  this.$browser.$$checkUrlChange = this.browserCheckUrlChange;

  this.startIndex = this.newStartIndex;
  this.endIndex = this.newEndIndex;

  if (this.isFirstRender) {
    this.isFirstRender = false;
    var firstRenderStartIndex = this.$attrs.mdStartIndex ?
      this.$scope.$eval(this.$attrs.mdStartIndex) :
      this.container.topIndex;

    // The first call to virtualRepeatUpdate_ may not be when the virtual repeater is ready.
    // Introduce a slight delay so that the update happens when it is actually ready.
    this.$mdUtil.nextTick(function() {
      this.container.scrollToIndex(firstRenderStartIndex);
    }.bind(this));
  }

  this.isVirtualRepeatUpdating_ = false;
};


/**
 * @param {number} index Where the block is to be in the repeated list.
 * @return {!VirtualRepeatController.Block} A new or pooled block to place at the specified index.
 * @private
 */
VirtualRepeatController.prototype.getBlock_ = function(index) {
  if (this.pooledBlocks.length) {
    return this.pooledBlocks.pop();
  }

  var block;
  this.transclude(angular.bind(this, function(clone, scope) {
    block = {
      element: clone,
      new: true,
      scope: scope
    };

    this.updateScope_(scope, index);
    this.parentNode.appendChild(clone[0]);
  }));

  return block;
};


/**
 * Updates and if not in a digest cycle, digests the specified block's scope to the data
 * at the specified index.
 * @param {!VirtualRepeatController.Block} block The block whose scope should be updated.
 * @param {number} index The index to set.
 * @private
 */
VirtualRepeatController.prototype.updateBlock_ = function(block, index) {
  this.blocks[index] = block;

  if (!block.new &&
      (block.scope.$index === index && block.scope[this.repeatName] === this.items[index])) {
    return;
  }
  block.new = false;

  // Update and digest the block's scope.
  this.updateScope_(block.scope, index);

  // Perform digest before reattaching the block.
  // Any resulting synchronous dom mutations should be much faster as a result.
  // This might break some directives, but I'm going to try it for now.
  if (!this.$rootScope.$$phase) {
    block.scope.$digest();
  }
};


/**
 * Updates scope to the data at the specified index.
 * @param {!angular.Scope} scope The scope which should be updated.
 * @param {number} index The index to set.
 * @private
 */
VirtualRepeatController.prototype.updateScope_ = function(scope, index) {
  scope.$index = index;
  scope[this.repeatName] = this.items && this.items[index];
  if (this.extraName) scope[this.extraName(this.$scope)] = this.items[index];
};


/**
 * Pools the block at the specified index (Pulls its element out of the dom and stores it).
 * @param {number} index The index at which the block to pool is stored.
 * @private
 */
VirtualRepeatController.prototype.poolBlock_ = function(index) {
  this.pooledBlocks.push(this.blocks[index]);
  this.parentNode.removeChild(this.blocks[index].element[0]);
  delete this.blocks[index];
};


/**
 * Produces a dom fragment containing the elements from the list of blocks.
 * @param {!Array<!VirtualRepeatController.Block>} blocks The blocks whose elements
 *     should be added to the document fragment.
 * @return {DocumentFragment}
 * @private
 */
VirtualRepeatController.prototype.domFragmentFromBlocks_ = function(blocks) {
  var fragment = this.$document[0].createDocumentFragment();
  blocks.forEach(function(block) {
    fragment.appendChild(block.element[0]);
  });
  return fragment;
};


/**
 * Updates start and end indexes based on length of repeated items and container size.
 * @private
 */
VirtualRepeatController.prototype.updateIndexes_ = function() {
  var itemsLength = this.items ? this.items.length : 0;
  var containerLength = Math.ceil(this.container.getSize() / this.itemSize);

  this.newStartIndex = Math.max(0, Math.min(
      itemsLength - containerLength,
      Math.floor(this.container.getScrollOffset() / this.itemSize)));
  this.newVisibleEnd = this.newStartIndex + containerLength + NUM_EXTRA;
  this.newEndIndex = Math.min(itemsLength, this.newVisibleEnd);
  this.newStartIndex = Math.max(0, this.newStartIndex - NUM_EXTRA);
};

/**
 * This VirtualRepeatModelArrayLike class enforces the interface requirements
 * for infinite scrolling within a mdVirtualRepeatContainer. An object with this
 * interface must implement the following interface with two (2) methods:
 *
 * getItemAtIndex: function(index) -> item at that index or null if it is not yet
 *     loaded (It should start downloading the item in that case).
 *
 * getLength: function() -> number The data legnth to which the repeater container
 *     should be sized. Ideally, when the count is known, this method should return it.
 *     Otherwise, return a higher number than the currently loaded items to produce an
 *     infinite-scroll behavior.
 *
 * @usage
 * <hljs lang="html">
 *  <md-virtual-repeat-container md-orient-horizontal>
 *    <div md-virtual-repeat="i in items" md-on-demand>
 *      Hello {{i}}!
 *    </div>
 *  </md-virtual-repeat-container>
 * </hljs>
 *
 */
function VirtualRepeatModelArrayLike(model) {
  if (!angular.isFunction(model.getItemAtIndex) ||
      !angular.isFunction(model.getLength)) {
    throw Error('When md-on-demand is enabled, the Object passed to md-virtual-repeat must implement ' +
        'functions getItemAtIndex() and getLength() ');
  }

  this.model = model;
}


VirtualRepeatModelArrayLike.prototype.$$includeIndexes = function(start, end) {
  for (var i = start; i < end; i++) {
    if (!this.hasOwnProperty(i)) {
      this[i] = this.model.getItemAtIndex(i);
    }
  }
  this.length = this.model.getLength();
};

/**
 * @ngdoc directive
 * @name mdForceHeight
 * @module material.components.virtualRepeat
 * @restrict A
 * @description
 *
 * Force an element to have a certain px height. This is used in place of a style tag in order to
 * conform to the Content Security Policy regarding unsafe-inline style tags.
 *
 * @usage
 * <hljs lang="html">
 *   <div md-force-height="'100px'"></div>
 * </hljs>
 */
function ForceHeightDirective($mdUtil) {
  return {
    restrict: 'A',
    link: function(scope, element, attrs) {
      var height = scope.$eval(attrs.mdForceHeight) || null;

      if (height && element) {
        element[0].style.height = height;
      }
    }
  }
}
ForceHeightDirective.$inject = ['$mdUtil'];

})();
(function(){
"use strict";

/**
 * @ngdoc module
 * @name material.components.whiteframe
 */
MdWhiteframeDirective.$inject = ["$log"];
angular
  .module('material.components.whiteframe', ['material.core'])
  .directive('mdWhiteframe', MdWhiteframeDirective);

/**
 * @ngdoc directive
 * @module material.components.whiteframe
 * @name mdWhiteframe
 *
 * @description
 * The md-whiteframe directive allows you to apply an elevation shadow to an element.
 *
 * The attribute values needs to be a number between 1 and 24 or -1.
 * When set to -1 no style is applied.
 *
 * ### Notes
 * - If there is no value specified it defaults to 4dp.
 * - If the value is not valid it defaults to 4dp.

 * @usage
 * <hljs lang="html">
 * <div md-whiteframe="3">
 *   <span>Elevation of 3dp</span>
 * </div>
 * </hljs>
 *
 * <hljs lang="html">
 * <div md-whiteframe="-1">
 *   <span>No elevation shadow applied</span>
 * </div>
 * </hljs>
 *
 * <hljs lang="html">
 * <div ng-init="elevation = 5" md-whiteframe="{{elevation}}">
 *   <span>Elevation of 5dp with an interpolated value</span>
 * </div>
 * </hljs>
 */
function MdWhiteframeDirective($log) {
  var DISABLE_DP = -1;
  var MIN_DP = 1;
  var MAX_DP = 24;
  var DEFAULT_DP = 4;

  return {
    link: postLink
  };

  function postLink(scope, element, attr) {
    var oldClass = '';

    attr.$observe('mdWhiteframe', function(elevation) {
      elevation = parseInt(elevation, 10) || DEFAULT_DP;

      if (elevation != DISABLE_DP && (elevation > MAX_DP || elevation < MIN_DP)) {
        $log.warn('md-whiteframe attribute value is invalid. It should be a number between ' + MIN_DP + ' and ' + MAX_DP, element[0]);
        elevation = DEFAULT_DP;
      }

      var newClass = elevation == DISABLE_DP ? '' : 'md-whiteframe-' + elevation + 'dp';
      attr.$updateClass(newClass, oldClass);
      oldClass = newClass;
    });
  }
}


})();
(function(){
"use strict";


MdAutocompleteCtrl.$inject = ["$scope", "$element", "$mdUtil", "$mdConstant", "$mdTheming", "$window", "$animate", "$rootElement", "$attrs", "$q", "$log", "$mdLiveAnnouncer"];angular
    .module('material.components.autocomplete')
    .controller('MdAutocompleteCtrl', MdAutocompleteCtrl);

var ITEM_HEIGHT   = 48,
    MAX_ITEMS     = 5,
    MENU_PADDING  = 8,
    INPUT_PADDING = 2; // Padding provided by `md-input-container`

function MdAutocompleteCtrl ($scope, $element, $mdUtil, $mdConstant, $mdTheming, $window,
                             $animate, $rootElement, $attrs, $q, $log, $mdLiveAnnouncer) {

  // Internal Variables.
  var ctrl                 = this,
      itemParts            = $scope.itemsExpr.split(/ in /i),
      itemExpr             = itemParts[ 1 ],
      elements             = null,
      cache                = {},
      noBlur               = false,
      selectedItemWatchers = [],
      hasFocus             = false,
      fetchesInProgress    = 0,
      enableWrapScroll     = null,
      inputModelCtrl       = null,
      debouncedOnResize    = $mdUtil.debounce(onWindowResize);

  // Public Exported Variables with handlers
  defineProperty('hidden', handleHiddenChange, true);

  // Public Exported Variables
  ctrl.scope      = $scope;
  ctrl.parent     = $scope.$parent;
  ctrl.itemName   = itemParts[ 0 ];
  ctrl.matches    = [];
  ctrl.loading    = false;
  ctrl.hidden     = true;
  ctrl.index      = null;
  ctrl.id         = $mdUtil.nextUid();
  ctrl.isDisabled = null;
  ctrl.isRequired = null;
  ctrl.isReadonly = null;
  ctrl.hasNotFound = false;

  // Public Exported Methods
  ctrl.keydown                       = keydown;
  ctrl.blur                          = blur;
  ctrl.focus                         = focus;
  ctrl.clear                         = clearValue;
  ctrl.select                        = select;
  ctrl.listEnter                     = onListEnter;
  ctrl.listLeave                     = onListLeave;
  ctrl.mouseUp                       = onMouseup;
  ctrl.getCurrentDisplayValue        = getCurrentDisplayValue;
  ctrl.registerSelectedItemWatcher   = registerSelectedItemWatcher;
  ctrl.unregisterSelectedItemWatcher = unregisterSelectedItemWatcher;
  ctrl.notFoundVisible               = notFoundVisible;
  ctrl.loadingIsVisible              = loadingIsVisible;
  ctrl.positionDropdown              = positionDropdown;

  /**
   * Report types to be used for the $mdLiveAnnouncer
   * @enum {number} Unique flag id.
   */
  var ReportType = {
    Count: 1,
    Selected: 2
  };

  return init();

  //-- initialization methods

  /**
   * Initialize the controller, setup watchers, gather elements
   */
  function init () {

    $mdUtil.initOptionalProperties($scope, $attrs, {
      searchText: '',
      selectedItem: null,
      clearButton: false
    });

    $mdTheming($element);
    configureWatchers();
    $mdUtil.nextTick(function () {

      gatherElements();
      moveDropdown();

      // Forward all focus events to the input element when autofocus is enabled
      if ($scope.autofocus) {
        $element.on('focus', focusInputElement);
      }
    });
  }

  function updateModelValidators() {
    if (!$scope.requireMatch || !inputModelCtrl) return;

    inputModelCtrl.$setValidity('md-require-match', !!$scope.selectedItem || !$scope.searchText);
  }

  /**
   * Calculates the dropdown's position and applies the new styles to the menu element
   * @returns {*}
   */
  function positionDropdown () {
    if (!elements) {
      return $mdUtil.nextTick(positionDropdown, false, $scope);
    }

    var dropdownHeight = ($scope.dropdownItems || MAX_ITEMS) * ITEM_HEIGHT;

    var hrect  = elements.wrap.getBoundingClientRect(),
        vrect  = elements.snap.getBoundingClientRect(),
        root   = elements.root.getBoundingClientRect(),
        top    = vrect.bottom - root.top,
        bot    = root.bottom - vrect.top,
        left   = hrect.left - root.left,
        width  = hrect.width,
        offset = getVerticalOffset(),
        position = $scope.dropdownPosition,
        styles;

    // Automatically determine dropdown placement based on available space in viewport.
    if (!position) {
      position = (top > bot && root.height - top - MENU_PADDING < dropdownHeight) ? 'top' : 'bottom';
    }
    // Adjust the width to account for the padding provided by `md-input-container`
    if ($attrs.mdFloatingLabel) {
      left += INPUT_PADDING;
      width -= INPUT_PADDING * 2;
    }
    styles = {
      left:     left + 'px',
      minWidth: width + 'px',
      maxWidth: Math.max(hrect.right - root.left, root.right - hrect.left) - MENU_PADDING + 'px'
    };

    if (position === 'top') {
      styles.top       = 'auto';
      styles.bottom    = bot + 'px';
      styles.maxHeight = Math.min(dropdownHeight, hrect.top - root.top - MENU_PADDING) + 'px';
    } else {
      var bottomSpace = root.bottom - hrect.bottom - MENU_PADDING + $mdUtil.getViewportTop();

      styles.top       = (top - offset) + 'px';
      styles.bottom    = 'auto';
      styles.maxHeight = Math.min(dropdownHeight, bottomSpace) + 'px';
    }

    elements.$.scrollContainer.css(styles);
    $mdUtil.nextTick(correctHorizontalAlignment, false);

    /**
     * Calculates the vertical offset for floating label examples to account for ngMessages
     * @returns {number}
     */
    function getVerticalOffset () {
      var offset = 0;
      var inputContainer = $element.find('md-input-container');
      if (inputContainer.length) {
        var input = inputContainer.find('input');
        offset = inputContainer.prop('offsetHeight');
        offset -= input.prop('offsetTop');
        offset -= input.prop('offsetHeight');
        // add in the height left up top for the floating label text
        offset += inputContainer.prop('offsetTop');
      }
      return offset;
    }

    /**
     * Makes sure that the menu doesn't go off of the screen on either side.
     */
    function correctHorizontalAlignment () {
      var dropdown = elements.scrollContainer.getBoundingClientRect(),
          styles   = {};
      if (dropdown.right > root.right - MENU_PADDING) {
        styles.left = (hrect.right - dropdown.width) + 'px';
      }
      elements.$.scrollContainer.css(styles);
    }
  }

  /**
   * Moves the dropdown menu to the body tag in order to avoid z-index and overflow issues.
   */
  function moveDropdown () {
    if (!elements.$.root.length) return;
    $mdTheming(elements.$.scrollContainer);
    elements.$.scrollContainer.detach();
    elements.$.root.append(elements.$.scrollContainer);
    if ($animate.pin) $animate.pin(elements.$.scrollContainer, $rootElement);
  }

  /**
   * Sends focus to the input element.
   */
  function focusInputElement () {
    elements.input.focus();
  }

  /**
   * Sets up any watchers used by autocomplete
   */
  function configureWatchers () {
    var wait = parseInt($scope.delay, 10) || 0;

    $attrs.$observe('disabled', function (value) { ctrl.isDisabled = $mdUtil.parseAttributeBoolean(value, false); });
    $attrs.$observe('required', function (value) { ctrl.isRequired = $mdUtil.parseAttributeBoolean(value, false); });
    $attrs.$observe('readonly', function (value) { ctrl.isReadonly = $mdUtil.parseAttributeBoolean(value, false); });

    $scope.$watch('searchText', wait ? $mdUtil.debounce(handleSearchText, wait) : handleSearchText);
    $scope.$watch('selectedItem', selectedItemChange);

    angular.element($window).on('resize', debouncedOnResize);

    $scope.$on('$destroy', cleanup);
  }

  /**
   * Removes any events or leftover elements created by this controller
   */
  function cleanup () {
    if (!ctrl.hidden) {
      $mdUtil.enableScrolling();
    }

    angular.element($window).off('resize', debouncedOnResize);

    if ( elements ){
      var items = ['ul', 'scroller', 'scrollContainer', 'input'];
      angular.forEach(items, function(key){
        elements.$[key].remove();
      });
    }
  }

  /**
   * Event handler to be called whenever the window resizes.
   */
  function onWindowResize() {
    if (!ctrl.hidden) {
      positionDropdown();
    }
  }

  /**
   * Gathers all of the elements needed for this controller
   */
  function gatherElements () {

    var snapWrap = gatherSnapWrap();

    elements = {
      main:  $element[0],
      scrollContainer: $element[0].querySelector('.md-virtual-repeat-container'),
      scroller: $element[0].querySelector('.md-virtual-repeat-scroller'),
      ul:    $element.find('ul')[0],
      input: $element.find('input')[0],
      wrap:  snapWrap.wrap,
      snap:  snapWrap.snap,
      root:  document.body
    };

    elements.li   = elements.ul.getElementsByTagName('li');
    elements.$    = getAngularElements(elements);

    inputModelCtrl = elements.$.input.controller('ngModel');
  }

  /**
   * Gathers the snap and wrap elements
   *
   */
  function gatherSnapWrap() {
    var element;
    var value;
    for (element = $element; element.length; element = element.parent()) {
      value = element.attr('md-autocomplete-snap');
      if (angular.isDefined(value)) break;
    }

    if (element.length) {
      return {
        snap: element[0],
        wrap: (value.toLowerCase() === 'width') ? element[0] : $element.find('md-autocomplete-wrap')[0]
      };
    }

    var wrap = $element.find('md-autocomplete-wrap')[0];
    return {
      snap: wrap,
      wrap: wrap
    };
  }

  /**
   * Gathers angular-wrapped versions of each element
   * @param elements
   * @returns {{}}
   */
  function getAngularElements (elements) {
    var obj = {};
    for (var key in elements) {
      if (elements.hasOwnProperty(key)) obj[ key ] = angular.element(elements[ key ]);
    }
    return obj;
  }

  //-- event/change handlers

  /**
   * Handles changes to the `hidden` property.
   * @param hidden
   * @param oldHidden
   */
  function handleHiddenChange (hidden, oldHidden) {
    if (!hidden && oldHidden) {
      positionDropdown();

      // Report in polite mode, because the screenreader should finish the default description of
      // the input. element.
      reportMessages(true, ReportType.Count | ReportType.Selected);

      if (elements) {
        $mdUtil.disableScrollAround(elements.ul);
        enableWrapScroll = disableElementScrollEvents(angular.element(elements.wrap));
      }
    } else if (hidden && !oldHidden) {
      $mdUtil.enableScrolling();

      if (enableWrapScroll) {
        enableWrapScroll();
        enableWrapScroll = null;
      }
    }
  }

  /**
   * Disables scrolling for a specific element
   */
  function disableElementScrollEvents(element) {

    function preventDefault(e) {
      e.preventDefault();
    }

    element.on('wheel', preventDefault);
    element.on('touchmove', preventDefault);

    return function() {
      element.off('wheel', preventDefault);
      element.off('touchmove', preventDefault);
    };
  }

  /**
   * When the user mouses over the dropdown menu, ignore blur events.
   */
  function onListEnter () {
    noBlur = true;
  }

  /**
   * When the user's mouse leaves the menu, blur events may hide the menu again.
   */
  function onListLeave () {
    if (!hasFocus && !ctrl.hidden) elements.input.focus();
    noBlur = false;
    ctrl.hidden = shouldHide();
  }

  /**
   * When the mouse button is released, send focus back to the input field.
   */
  function onMouseup () {
    elements.input.focus();
  }

  /**
   * Handles changes to the selected item.
   * @param selectedItem
   * @param previousSelectedItem
   */
  function selectedItemChange (selectedItem, previousSelectedItem) {

    updateModelValidators();

    if (selectedItem) {
      getDisplayValue(selectedItem).then(function (val) {
        $scope.searchText = val;
        handleSelectedItemChange(selectedItem, previousSelectedItem);
      });
    } else if (previousSelectedItem && $scope.searchText) {
      getDisplayValue(previousSelectedItem).then(function(displayValue) {
        // Clear the searchText, when the selectedItem is set to null.
        // Do not clear the searchText, when the searchText isn't matching with the previous
        // selected item.
        if (angular.isString($scope.searchText)
          && displayValue.toString().toLowerCase() === $scope.searchText.toLowerCase()) {
          $scope.searchText = '';
        }
      });
    }

    if (selectedItem !== previousSelectedItem) announceItemChange();
  }

  /**
   * Use the user-defined expression to announce changes each time a new item is selected
   */
  function announceItemChange () {
    angular.isFunction($scope.itemChange) && $scope.itemChange(getItemAsNameVal($scope.selectedItem));
  }

  /**
   * Use the user-defined expression to announce changes each time the search text is changed
   */
  function announceTextChange () {
    angular.isFunction($scope.textChange) && $scope.textChange();
  }

  /**
   * Calls any external watchers listening for the selected item.  Used in conjunction with
   * `registerSelectedItemWatcher`.
   * @param selectedItem
   * @param previousSelectedItem
   */
  function handleSelectedItemChange (selectedItem, previousSelectedItem) {
    selectedItemWatchers.forEach(function (watcher) { watcher(selectedItem, previousSelectedItem); });
  }

  /**
   * Register a function to be called when the selected item changes.
   * @param cb
   */
  function registerSelectedItemWatcher (cb) {
    if (selectedItemWatchers.indexOf(cb) == -1) {
      selectedItemWatchers.push(cb);
    }
  }

  /**
   * Unregister a function previously registered for selected item changes.
   * @param cb
   */
  function unregisterSelectedItemWatcher (cb) {
    var i = selectedItemWatchers.indexOf(cb);
    if (i != -1) {
      selectedItemWatchers.splice(i, 1);
    }
  }

  /**
   * Handles changes to the searchText property.
   * @param searchText
   * @param previousSearchText
   */
  function handleSearchText (searchText, previousSearchText) {
    ctrl.index = getDefaultIndex();

    // do nothing on init
    if (searchText === previousSearchText) return;

    updateModelValidators();

    getDisplayValue($scope.selectedItem).then(function (val) {
      // clear selected item if search text no longer matches it
      if (searchText !== val) {
        $scope.selectedItem = null;


        // trigger change event if available
        if (searchText !== previousSearchText) announceTextChange();

        // cancel results if search text is not long enough
        if (!isMinLengthMet()) {
          ctrl.matches = [];

          setLoading(false);
          reportMessages(false, ReportType.Count);

        } else {
          handleQuery();
        }
      }
    });

  }

  /**
   * Handles input blur event, determines if the dropdown should hide.
   */
  function blur($event) {
    hasFocus = false;

    if (!noBlur) {
      ctrl.hidden = shouldHide();
      evalAttr('ngBlur', { $event: $event });
    }
  }

  /**
   * Force blur on input element
   * @param forceBlur
   */
  function doBlur(forceBlur) {
    if (forceBlur) {
      noBlur = false;
      hasFocus = false;
    }
    elements.input.blur();
  }

  /**
   * Handles input focus event, determines if the dropdown should show.
   */
  function focus($event) {
    hasFocus = true;

    if (isSearchable() && isMinLengthMet()) {
      handleQuery();
    }

    ctrl.hidden = shouldHide();

    evalAttr('ngFocus', { $event: $event });
  }

  /**
   * Handles keyboard input.
   * @param event
   */
  function keydown (event) {
    switch (event.keyCode) {
      case $mdConstant.KEY_CODE.DOWN_ARROW:
        if (ctrl.loading) return;
        event.stopPropagation();
        event.preventDefault();
        ctrl.index   = Math.min(ctrl.index + 1, ctrl.matches.length - 1);
        updateScroll();
        reportMessages(false, ReportType.Selected);
        break;
      case $mdConstant.KEY_CODE.UP_ARROW:
        if (ctrl.loading) return;
        event.stopPropagation();
        event.preventDefault();
        ctrl.index   = ctrl.index < 0 ? ctrl.matches.length - 1 : Math.max(0, ctrl.index - 1);
        updateScroll();
        reportMessages(false, ReportType.Selected);
        break;
      case $mdConstant.KEY_CODE.TAB:
        // If we hit tab, assume that we've left the list so it will close
        onListLeave();

        if (ctrl.hidden || ctrl.loading || ctrl.index < 0 || ctrl.matches.length < 1) return;
        select(ctrl.index);
        break;
      case $mdConstant.KEY_CODE.ENTER:
        if (ctrl.hidden || ctrl.loading || ctrl.index < 0 || ctrl.matches.length < 1) return;
        if (hasSelection()) return;
        event.stopPropagation();
        event.preventDefault();
        select(ctrl.index);
        break;
      case $mdConstant.KEY_CODE.ESCAPE:
        event.preventDefault(); // Prevent browser from always clearing input
        if (!shouldProcessEscape()) return;
        event.stopPropagation();

        clearSelectedItem();
        if ($scope.searchText && hasEscapeOption('clear')) {
          clearSearchText();
        }

        // Manually hide (needed for mdNotFound support)
        ctrl.hidden = true;

        if (hasEscapeOption('blur')) {
          // Force the component to blur if they hit escape
          doBlur(true);
        }

        break;
      default:
    }
  }

  //-- getters

  /**
   * Returns the minimum length needed to display the dropdown.
   * @returns {*}
   */
  function getMinLength () {
    return angular.isNumber($scope.minLength) ? $scope.minLength : 1;
  }

  /**
   * Returns the display value for an item.
   * @param item
   * @returns {*}
   */
  function getDisplayValue (item) {
    return $q.when(getItemText(item) || item).then(function(itemText) {
      if (itemText && !angular.isString(itemText)) {
        $log.warn('md-autocomplete: Could not resolve display value to a string. ' +
          'Please check the `md-item-text` attribute.');
      }

      return itemText;
    });

    /**
     * Getter function to invoke user-defined expression (in the directive)
     * to convert your object to a single string.
     */
    function getItemText (item) {
      return (item && $scope.itemText) ? $scope.itemText(getItemAsNameVal(item)) : null;
    }
  }

  /**
   * Returns the locals object for compiling item templates.
   * @param item
   * @returns {{}}
   */
  function getItemAsNameVal (item) {
    if (!item) return undefined;

    var locals = {};
    if (ctrl.itemName) locals[ ctrl.itemName ] = item;

    return locals;
  }

  /**
   * Returns the default index based on whether or not autoselect is enabled.
   * @returns {number}
   */
  function getDefaultIndex () {
    return $scope.autoselect ? 0 : -1;
  }

  /**
   * Sets the loading parameter and updates the hidden state.
   * @param value {boolean} Whether or not the component is currently loading.
   */
  function setLoading(value) {
    if (ctrl.loading != value) {
      ctrl.loading = value;
    }

    // Always refresh the hidden variable as something else might have changed
    ctrl.hidden = shouldHide();
  }

  /**
   * Determines if the menu should be hidden.
   * @returns {boolean}
   */
  function shouldHide () {
    if (!isSearchable()) return true;    // Hide when not able to query
    else return !shouldShow();            // Hide when the dropdown is not able to show.
  }

  /**
   * Determines whether the autocomplete is able to query within the current state.
   * @returns {boolean}
   */
  function isSearchable() {
    if (ctrl.loading && !hasMatches()) return false; // No query when query is in progress.
    else if (hasSelection()) return false;           // No query if there is already a selection
    else if (!hasFocus) return false;                // No query if the input does not have focus
    return true;
  }

  /**
   * Determines if the escape keydown should be processed
   * @returns {boolean}
   */
  function shouldProcessEscape() {
    return hasEscapeOption('blur') || !ctrl.hidden || ctrl.loading || hasEscapeOption('clear') && $scope.searchText;
  }

  /**
   * Determines if an escape option is set
   * @returns {boolean}
   */
  function hasEscapeOption(option) {
    return !$scope.escapeOptions || $scope.escapeOptions.toLowerCase().indexOf(option) !== -1;
  }

  /**
   * Determines if the menu should be shown.
   * @returns {boolean}
   */
  function shouldShow() {
    return (isMinLengthMet() && hasMatches()) || notFoundVisible();
  }

  /**
   * Returns true if the search text has matches.
   * @returns {boolean}
   */
  function hasMatches() {
    return ctrl.matches.length ? true : false;
  }

  /**
   * Returns true if the autocomplete has a valid selection.
   * @returns {boolean}
   */
  function hasSelection() {
    return ctrl.scope.selectedItem ? true : false;
  }

  /**
   * Returns true if the loading indicator is, or should be, visible.
   * @returns {boolean}
   */
  function loadingIsVisible() {
    return ctrl.loading && !hasSelection();
  }

  /**
   * Returns the display value of the current item.
   * @returns {*}
   */
  function getCurrentDisplayValue () {
    return getDisplayValue(ctrl.matches[ ctrl.index ]);
  }

  /**
   * Determines if the minimum length is met by the search text.
   * @returns {*}
   */
  function isMinLengthMet () {
    return ($scope.searchText || '').length >= getMinLength();
  }

  //-- actions

  /**
   * Defines a public property with a handler and a default value.
   * @param key
   * @param handler
   * @param value
   */
  function defineProperty (key, handler, value) {
    Object.defineProperty(ctrl, key, {
      get: function () { return value; },
      set: function (newValue) {
        var oldValue = value;
        value        = newValue;
        handler(newValue, oldValue);
      }
    });
  }

  /**
   * Selects the item at the given index.
   * @param index
   */
  function select (index) {
    //-- force form to update state for validation
    $mdUtil.nextTick(function () {
      getDisplayValue(ctrl.matches[ index ]).then(function (val) {
        var ngModel = elements.$.input.controller('ngModel');
        ngModel.$setViewValue(val);
        ngModel.$render();
      }).finally(function () {
        $scope.selectedItem = ctrl.matches[ index ];
        setLoading(false);
      });
    }, false);
  }

  /**
   * Clears the searchText value and selected item.
   */
  function clearValue () {
    clearSelectedItem();
    clearSearchText();
  }

  /**
   * Clears the selected item
   */
  function clearSelectedItem () {
    // Reset our variables
    ctrl.index = 0;
    ctrl.matches = [];
  }

  /**
   * Clears the searchText value
   */
  function clearSearchText () {
    // Set the loading to true so we don't see flashes of content.
    // The flashing will only occur when an async request is running.
    // So the loading process will stop when the results had been retrieved.
    setLoading(true);

    $scope.searchText = '';

    // Normally, triggering the change / input event is unnecessary, because the browser detects it properly.
    // But some browsers are not detecting it properly, which means that we have to trigger the event.
    // Using the `input` is not working properly, because for example IE11 is not supporting the `input` event.
    // The `change` event is a good alternative and is supported by all supported browsers.
    var eventObj = document.createEvent('CustomEvent');
    eventObj.initCustomEvent('change', true, true, { value: '' });
    elements.input.dispatchEvent(eventObj);

    // For some reason, firing the above event resets the value of $scope.searchText if
    // $scope.searchText has a space character at the end, so we blank it one more time and then
    // focus.
    elements.input.blur();
    $scope.searchText = '';
    elements.input.focus();
  }

  /**
   * Fetches the results for the provided search text.
   * @param searchText
   */
  function fetchResults (searchText) {
    var items = $scope.$parent.$eval(itemExpr),
        term  = searchText.toLowerCase(),
        isList = angular.isArray(items),
        isPromise = !!items.then; // Every promise should contain a `then` property

    if (isList) onResultsRetrieved(items);
    else if (isPromise) handleAsyncResults(items);

    function handleAsyncResults(items) {
      if ( !items ) return;

      items = $q.when(items);
      fetchesInProgress++;
      setLoading(true);

      $mdUtil.nextTick(function () {
          items
            .then(onResultsRetrieved)
            .finally(function(){
              if (--fetchesInProgress === 0) {
                setLoading(false);
              }
            });
      },true, $scope);
    }

    function onResultsRetrieved(matches) {
      cache[term] = matches;

      // Just cache the results if the request is now outdated.
      // The request becomes outdated, when the new searchText has changed during the result fetching.
      if ((searchText || '') !== ($scope.searchText || '')) {
        return;
      }

      handleResults(matches);
    }
  }


  /**
   * Reports given message types to supported screenreaders.
   * @param {boolean} isPolite Whether the announcement should be polite.
   * @param {!number} types Message flags to be reported to the screenreader.
   */
  function reportMessages(isPolite, types) {

    var politeness = isPolite ? 'polite' : 'assertive';
    var messages = [];

    if (types & ReportType.Selected && ctrl.index !== -1) {
      messages.push(getCurrentDisplayValue());
    }

    if (types & ReportType.Count) {
      messages.push($q.resolve(getCountMessage()));
    }

    $q.all(messages).then(function(data) {
      $mdLiveAnnouncer.announce(data.join(' '), politeness);
    });

  }

  /**
   * Returns the ARIA message for how many results match the current query.
   * @returns {*}
   */
  function getCountMessage () {
    switch (ctrl.matches.length) {
      case 0:
        return 'There are no matches available.';
      case 1:
        return 'There is 1 match available.';
      default:
        return 'There are ' + ctrl.matches.length + ' matches available.';
    }
  }

  /**
   * Makes sure that the focused element is within view.
   */
  function updateScroll () {
    if (!elements.li[0]) return;
    var height = elements.li[0].offsetHeight,
        top = height * ctrl.index,
        bot = top + height,
        hgt = elements.scroller.clientHeight,
        scrollTop = elements.scroller.scrollTop;
    if (top < scrollTop) {
      scrollTo(top);
    } else if (bot > scrollTop + hgt) {
      scrollTo(bot - hgt);
    }
  }

  function isPromiseFetching() {
    return fetchesInProgress !== 0;
  }

  function scrollTo (offset) {
    elements.$.scrollContainer.controller('mdVirtualRepeatContainer').scrollTo(offset);
  }

  function notFoundVisible () {
    var textLength = (ctrl.scope.searchText || '').length;

    return ctrl.hasNotFound && !hasMatches() && (!ctrl.loading || isPromiseFetching()) && textLength >= getMinLength() && (hasFocus || noBlur) && !hasSelection();
  }

  /**
   * Starts the query to gather the results for the current searchText.  Attempts to return cached
   * results first, then forwards the process to `fetchResults` if necessary.
   */
  function handleQuery () {
    var searchText = $scope.searchText || '';
    var term = searchText.toLowerCase();

    // If caching is enabled and the current searchText is stored in the cache
    if (!$scope.noCache && cache[term]) {
      // The results should be handled as same as a normal un-cached request does.
      handleResults(cache[term]);
    } else {
      fetchResults(searchText);
    }

    ctrl.hidden = shouldHide();
  }

  /**
   * Handles the retrieved results by showing them in the autocompletes dropdown.
   * @param results Retrieved results
   */
  function handleResults(results) {
    ctrl.matches = results;
    ctrl.hidden  = shouldHide();

    // If loading is in progress, then we'll end the progress. This is needed for example,
    // when the `clear` button was clicked, because there we always show the loading process, to prevent flashing.
    if (ctrl.loading) setLoading(false);

    if ($scope.selectOnMatch) selectItemOnMatch();

    positionDropdown();
    reportMessages(true, ReportType.Count);
  }

  /**
   * If there is only one matching item and the search text matches its display value exactly,
   * automatically select that item.  Note: This function is only called if the user uses the
   * `md-select-on-match` flag.
   */
  function selectItemOnMatch () {
    var searchText = $scope.searchText,
        matches    = ctrl.matches,
        item       = matches[ 0 ];
    if (matches.length === 1) getDisplayValue(item).then(function (displayValue) {
      var isMatching = searchText == displayValue;
      if ($scope.matchInsensitive && !isMatching) {
        isMatching = searchText.toLowerCase() == displayValue.toLowerCase();
      }

      if (isMatching) select(0);
    });
  }

  /**
   * Evaluates an attribute expression against the parent scope.
   * @param {String} attr Name of the attribute to be evaluated.
   * @param {Object?} locals Properties to be injected into the evaluation context.
   */
 function evalAttr(attr, locals) {
    if ($attrs[attr]) {
      $scope.$parent.$eval($attrs[attr], locals || {});
    }
  }

}

})();
(function(){
"use strict";


MdAutocomplete.$inject = ["$$mdSvgRegistry"];angular
    .module('material.components.autocomplete')
    .directive('mdAutocomplete', MdAutocomplete);

/**
 * @ngdoc directive
 * @name mdAutocomplete
 * @module material.components.autocomplete
 *
 * @description
 * `<md-autocomplete>` is a special input component with a drop-down of all possible matches to a
 *     custom query. This component allows you to provide real-time suggestions as the user types
 *     in the input area.
 *
 * To start, you will need to specify the required parameters and provide a template for your
 *     results. The content inside `md-autocomplete` will be treated as a template.
 *
 * In more complex cases, you may want to include other content such as a message to display when
 *     no matches were found.  You can do this by wrapping your template in `md-item-template` and
 *     adding a tag for `md-not-found`.  An example of this is shown below.
 *
 * To reset the displayed value you must clear both values for `md-search-text` and `md-selected-item`.
 *
 * ### Validation
 *
 * You can use `ng-messages` to include validation the same way that you would normally validate;
 *     however, if you want to replicate a standard input with a floating label, you will have to
 *     do the following:
 *
 * - Make sure that your template is wrapped in `md-item-template`
 * - Add your `ng-messages` code inside of `md-autocomplete`
 * - Add your validation properties to `md-autocomplete` (ie. `required`)
 * - Add a `name` to `md-autocomplete` (to be used on the generated `input`)
 *
 * There is an example below of how this should look.
 *
 * ### Snapping Drop-Down
 *
 * You can cause the autocomplete drop-down to snap to an ancestor element by applying the
 *     `md-autocomplete-snap` attribute to that element. You can also snap to the width of
 *     the `md-autocomplete-snap` element by setting the attribute's value to `width`
 *     (ie. `md-autocomplete-snap="width"`).
 *
 * ### Notes
 *
 * **Autocomplete Dropdown Items Rendering**
 *
 * The `md-autocomplete` uses the the <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeat</a>
 * directive for displaying the results inside of the dropdown.<br/>
 *
 * > When encountering issues regarding the item template please take a look at the
 *   <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> documentation.
 *
 * **Autocomplete inside of a Virtual Repeat**
 *
 * When using the `md-autocomplete` directive inside of a
 * <a ng-href="api/directive/mdVirtualRepeatContainer">VirtualRepeatContainer</a> the dropdown items might
 * not update properly, because caching of the results is enabled by default.
 *
 * The autocomplete will then show invalid dropdown items, because the VirtualRepeat only updates the
 * scope bindings, rather than re-creating the `md-autocomplete` and the previous cached results will be used.
 *
 * > To avoid such problems ensure that the autocomplete does not cache any results.
 *
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-no-cache="true"
 *       md-selected-item="selectedItem"
 *       md-items="item in items"
 *       md-search-text="searchText"
 *       md-item-text="item.display">
 *     <span>{{ item.display }}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 *
 *
 * @param {expression} md-items An expression in the format of `item in results` to iterate over
 *     matches for your search.<br/><br/>
 *     The `results` expression can be also a function, which returns the results synchronously
 *     or asynchronously (per Promise)
 * @param {expression=} md-selected-item-change An expression to be run each time a new item is
 *     selected
 * @param {expression=} md-search-text-change An expression to be run each time the search text
 *     updates
 * @param {expression=} md-search-text A model to bind the search query text to
 * @param {object=} md-selected-item A model to bind the selected item to
 * @param {expression=} md-item-text An expression that will convert your object to a single string.
 * @param {string=} placeholder Placeholder text that will be forwarded to the input.
 * @param {boolean=} md-no-cache Disables the internal caching that happens in autocomplete
 * @param {boolean=} ng-disabled Determines whether or not to disable the input field
 * @param {boolean=} md-require-match When set to true, the autocomplete will add a validator,
 *     which will evaluate to false, when no item is currently selected.
 * @param {number=} md-min-length Specifies the minimum length of text before autocomplete will
 *     make suggestions
 * @param {number=} md-delay Specifies the amount of time (in milliseconds) to wait before looking
 *     for results
 * @param {boolean=} md-clear-button Whether the clear button for the autocomplete input should show up or not.
 * @param {boolean=} md-autofocus If true, the autocomplete will be automatically focused when a `$mdDialog`,
 *     `$mdBottomsheet` or `$mdSidenav`, which contains the autocomplete, is opening. <br/><br/>
 *     Also the autocomplete will immediately focus the input element.
 * @param {boolean=} md-no-asterisk When present, asterisk will not be appended to the floating label
 * @param {boolean=} md-autoselect If set to true, the first item will be automatically selected
 *     in the dropdown upon open.
 * @param {string=} md-menu-class This will be applied to the dropdown menu for styling
 * @param {string=} md-floating-label This will add a floating label to autocomplete and wrap it in
 *     `md-input-container`
 * @param {string=} md-input-name The name attribute given to the input element to be used with
 *     FormController
 * @param {string=} md-select-on-focus When present the inputs text will be automatically selected
 *     on focus.
 * @param {string=} md-input-id An ID to be added to the input element
 * @param {number=} md-input-minlength The minimum length for the input's value for validation
 * @param {number=} md-input-maxlength The maximum length for the input's value for validation
 * @param {boolean=} md-select-on-match When set, autocomplete will automatically select exact
 *     the item if the search text is an exact match. <br/><br/>
 *     Exact match means that there is only one match showing up.
 * @param {boolean=} md-match-case-insensitive When set and using `md-select-on-match`, autocomplete
 *     will select on case-insensitive match
 * @param {string=} md-escape-options Override escape key logic. Default is `blur clear`.<br/>
 *     Options: `blur | clear`, `none`
 * @param {string=} md-dropdown-items Specifies the maximum amount of items to be shown in
 *     the dropdown.<br/><br/>
 *     When the dropdown doesn't fit into the viewport, the dropdown will shrink
 *     as less as possible.
 * @param {string=} md-dropdown-position Overrides the default dropdown position. Options: `top`, `bottom`.
 * @param {string=} ng-trim If set to false, the search text will be not trimmed automatically.
 *     Defaults to true.
 * @param {string=} ng-pattern Adds the pattern validator to the ngModel of the search text.
 *     [ngPattern Directive](https://docs.angularjs.org/api/ng/directive/ngPattern)
 *
 * @usage
 * ### Basic Example
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-autocomplete>
 * </hljs>
 *
 * ### Example with "not found" message
 * <hljs lang="html">
 * <md-autocomplete
 *     md-selected-item="selectedItem"
 *     md-search-text="searchText"
 *     md-items="item in getMatches(searchText)"
 *     md-item-text="item.display">
 *   <md-item-template>
 *     <span md-highlight-text="searchText">{{item.display}}</span>
 *   </md-item-template>
 *   <md-not-found>
 *     No matches found.
 *   </md-not-found>
 * </md-autocomplete>
 * </hljs>
 *
 * In this example, our code utilizes `md-item-template` and `md-not-found` to specify the
 *     different parts that make up our component.
 *
 * ### Clear button for the input
 * By default, for floating label autocomplete's the clear button is not showing up
 * ([See specs](https://material.google.com/components/text-fields.html#text-fields-auto-complete-text-field))
 *
 * Nevertheless, developers are able to explicitly toggle the clear button for all types of autocomplete's.
 *
 * <hljs lang="html">
 *   <md-autocomplete ... md-clear-button="true"></md-autocomplete>
 *   <md-autocomplete ... md-clear-button="false"></md-autocomplete>
 * </hljs>
 *
 * ### Example with validation
 * <hljs lang="html">
 * <form name="autocompleteForm">
 *   <md-autocomplete
 *       required
 *       md-input-name="autocomplete"
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in getMatches(searchText)"
 *       md-item-text="item.display">
 *     <md-item-template>
 *       <span md-highlight-text="searchText">{{item.display}}</span>
 *     </md-item-template>
 *     <div ng-messages="autocompleteForm.autocomplete.$error">
 *       <div ng-message="required">This field is required</div>
 *     </div>
 *   </md-autocomplete>
 * </form>
 * </hljs>
 *
 * In this example, our code utilizes `md-item-template` and `ng-messages` to specify
 *     input validation for the field.
 *
 * ### Asynchronous Results
 * The autocomplete items expression also supports promises, which will resolve with the query results.
 *
 * <hljs lang="js">
 *   function AppController($scope, $http) {
 *     $scope.query = function(searchText) {
 *       return $http
 *         .get(BACKEND_URL + '/items/' + searchText)
 *         .then(function(data) {
 *           // Map the response object to the data object.
 *           return data;
 *         });
 *     };
 *   }
 * </hljs>
 *
 * <hljs lang="html">
 *   <md-autocomplete
 *       md-selected-item="selectedItem"
 *       md-search-text="searchText"
 *       md-items="item in query(searchText)">
 *     <md-item-template>
 *       <span md-highlight-text="searchText">{{item}}</span>
 *     </md-item-template>
 * </md-autocomplete>
 * </hljs>
 *
 */

function MdAutocomplete ($$mdSvgRegistry) {

  return {
    controller:   'MdAutocompleteCtrl',
    controllerAs: '$mdAutocompleteCtrl',
    scope:        {
      inputName:        '@mdInputName',
      inputMinlength:   '@mdInputMinlength',
      inputMaxlength:   '@mdInputMaxlength',
      searchText:       '=?mdSearchText',
      selectedItem:     '=?mdSelectedItem',
      itemsExpr:        '@mdItems',
      itemText:         '&mdItemText',
      placeholder:      '@placeholder',
      noCache:          '=?mdNoCache',
      requireMatch:     '=?mdRequireMatch',
      selectOnMatch:    '=?mdSelectOnMatch',
      matchInsensitive: '=?mdMatchCaseInsensitive',
      itemChange:       '&?mdSelectedItemChange',
      textChange:       '&?mdSearchTextChange',
      minLength:        '=?mdMinLength',
      delay:            '=?mdDelay',
      autofocus:        '=?mdAutofocus',
      floatingLabel:    '@?mdFloatingLabel',
      autoselect:       '=?mdAutoselect',
      menuClass:        '@?mdMenuClass',
      inputId:          '@?mdInputId',
      escapeOptions:    '@?mdEscapeOptions',
      dropdownItems:    '=?mdDropdownItems',
      dropdownPosition: '@?mdDropdownPosition',
      clearButton:      '=?mdClearButton'
    },
    compile: function(tElement, tAttrs) {
      var attributes = ['md-select-on-focus', 'md-no-asterisk', 'ng-trim', 'ng-pattern'];
      var input = tElement.find('input');

      attributes.forEach(function(attribute) {
        var attrValue = tAttrs[tAttrs.$normalize(attribute)];

        if (attrValue !== null) {
          input.attr(attribute, attrValue);
        }
      });

      return function(scope, element, attrs, ctrl) {
        // Retrieve the state of using a md-not-found template by using our attribute, which will
        // be added to the element in the template function.
        ctrl.hasNotFound = !!element.attr('md-has-not-found');

        // By default the inset autocomplete should show the clear button when not explicitly overwritten.
        if (!angular.isDefined(attrs.mdClearButton) && !scope.floatingLabel) {
          scope.clearButton = true;
        }
      }
    },
    template:     function (element, attr) {
      var noItemsTemplate = getNoItemsTemplate(),
          itemTemplate    = getItemTemplate(),
          leftover        = element.html(),
          tabindex        = attr.tabindex;

      // Set our attribute for the link function above which runs later.
      // We will set an attribute, because otherwise the stored variables will be trashed when
      // removing the element is hidden while retrieving the template. For example when using ngIf.
      if (noItemsTemplate) element.attr('md-has-not-found', true);

      // Always set our tabindex of the autocomplete directive to -1, because our input
      // will hold the actual tabindex.
      element.attr('tabindex', '-1');

      return '\
        <md-autocomplete-wrap\
            ng-class="{ \'md-whiteframe-z1\': !floatingLabel, \
                        \'md-menu-showing\': !$mdAutocompleteCtrl.hidden, \
                        \'md-show-clear-button\': !!clearButton }">\
          ' + getInputElement() + '\
          ' + getClearButton() + '\
          <md-progress-linear\
              class="' + (attr.mdFloatingLabel ? 'md-inline' : '') + '"\
              ng-if="$mdAutocompleteCtrl.loadingIsVisible()"\
              md-mode="indeterminate"></md-progress-linear>\
          <md-virtual-repeat-container\
              md-auto-shrink\
              md-auto-shrink-min="1"\
              ng-mouseenter="$mdAutocompleteCtrl.listEnter()"\
              ng-mouseleave="$mdAutocompleteCtrl.listLeave()"\
              ng-mouseup="$mdAutocompleteCtrl.mouseUp()"\
              ng-hide="$mdAutocompleteCtrl.hidden"\
              class="md-autocomplete-suggestions-container md-whiteframe-z1"\
              ng-class="{ \'md-not-found\': $mdAutocompleteCtrl.notFoundVisible() }"\
              role="presentation">\
            <ul class="md-autocomplete-suggestions"\
                ng-class="::menuClass"\
                id="ul-{{$mdAutocompleteCtrl.id}}">\
              <li md-virtual-repeat="item in $mdAutocompleteCtrl.matches"\
                  ng-class="{ selected: $index === $mdAutocompleteCtrl.index }"\
                  ng-click="$mdAutocompleteCtrl.select($index)"\
                  md-extra-name="$mdAutocompleteCtrl.itemName">\
                  ' + itemTemplate + '\
                  </li>' + noItemsTemplate + '\
            </ul>\
          </md-virtual-repeat-container>\
        </md-autocomplete-wrap>';

      function getItemTemplate() {
        var templateTag = element.find('md-item-template').detach(),
            html = templateTag.length ? templateTag.html() : element.html();
        if (!templateTag.length) element.empty();
        return '<md-autocomplete-parent-scope md-autocomplete-replace>' + html + '</md-autocomplete-parent-scope>';
      }

      function getNoItemsTemplate() {
        var templateTag = element.find('md-not-found').detach(),
            template = templateTag.length ? templateTag.html() : '';
        return template
            ? '<li ng-if="$mdAutocompleteCtrl.notFoundVisible()"\
                         md-autocomplete-parent-scope>' + template + '</li>'
            : '';

      }

      function getInputElement () {
        if (attr.mdFloatingLabel) {
          return '\
            <md-input-container ng-if="floatingLabel">\
              <label>{{floatingLabel}}</label>\
              <input type="search"\
                  ' + (tabindex != null ? 'tabindex="' + tabindex + '"' : '') + '\
                  id="{{ inputId || \'fl-input-\' + $mdAutocompleteCtrl.id }}"\
                  name="{{inputName}}"\
                  autocomplete="off"\
                  ng-required="$mdAutocompleteCtrl.isRequired"\
                  ng-readonly="$mdAutocompleteCtrl.isReadonly"\
                  ng-minlength="inputMinlength"\
                  ng-maxlength="inputMaxlength"\
                  ng-disabled="$mdAutocompleteCtrl.isDisabled"\
                  ng-model="$mdAutocompleteCtrl.scope.searchText"\
                  ng-model-options="{ allowInvalid: true }"\
                  ng-keydown="$mdAutocompleteCtrl.keydown($event)"\
                  ng-blur="$mdAutocompleteCtrl.blur($event)"\
                  ng-focus="$mdAutocompleteCtrl.focus($event)"\
                  aria-owns="ul-{{$mdAutocompleteCtrl.id}}"\
                  aria-label="{{floatingLabel}}"\
                  aria-autocomplete="list"\
                  role="combobox"\
                  aria-haspopup="true"\
                  aria-activedescendant=""\
                  aria-expanded="{{!$mdAutocompleteCtrl.hidden}}"/>\
              <div md-autocomplete-parent-scope md-autocomplete-replace>' + leftover + '</div>\
            </md-input-container>';
        } else {
          return '\
            <input type="search"\
                ' + (tabindex != null ? 'tabindex="' + tabindex + '"' : '') + '\
                id="{{ inputId || \'input-\' + $mdAutocompleteCtrl.id }}"\
                name="{{inputName}}"\
                ng-if="!floatingLabel"\
                autocomplete="off"\
                ng-required="$mdAutocompleteCtrl.isRequired"\
                ng-disabled="$mdAutocompleteCtrl.isDisabled"\
                ng-readonly="$mdAutocompleteCtrl.isReadonly"\
                ng-minlength="inputMinlength"\
                ng-maxlength="inputMaxlength"\
                ng-model="$mdAutocompleteCtrl.scope.searchText"\
                ng-keydown="$mdAutocompleteCtrl.keydown($event)"\
                ng-blur="$mdAutocompleteCtrl.blur($event)"\
                ng-focus="$mdAutocompleteCtrl.focus($event)"\
                placeholder="{{placeholder}}"\
                aria-owns="ul-{{$mdAutocompleteCtrl.id}}"\
                aria-label="{{placeholder}}"\
                aria-autocomplete="list"\
                role="combobox"\
                aria-haspopup="true"\
                aria-activedescendant=""\
                aria-expanded="{{!$mdAutocompleteCtrl.hidden}}"/>';
        }
      }

      function getClearButton() {
        return '' +
          '<button ' +
              'type="button" ' +
              'aria-label="Clear Input" ' +
              'tabindex="-1" ' +
              'ng-if="clearButton && $mdAutocompleteCtrl.scope.searchText" ' +
              'ng-click="$mdAutocompleteCtrl.clear($event)">' +
            '<md-icon md-svg-src="' + $$mdSvgRegistry.mdClose + '"></md-icon>' +
          '</button>';
        }
    }
  };
}

})();
(function(){
"use strict";


MdAutocompleteItemScopeDirective.$inject = ["$compile", "$mdUtil"];angular
  .module('material.components.autocomplete')
  .directive('mdAutocompleteParentScope', MdAutocompleteItemScopeDirective);

function MdAutocompleteItemScopeDirective($compile, $mdUtil) {
  return {
    restrict: 'AE',
    compile: compile,
    terminal: true,
    transclude: 'element'
  };

  function compile(tElement, tAttr, transclude) {
    return function postLink(scope, element, attr) {
      var ctrl = scope.$mdAutocompleteCtrl;
      var newScope = ctrl.parent.$new();
      var itemName = ctrl.itemName;

      // Watch for changes to our scope's variables and copy them to the new scope
      watchVariable('$index', '$index');
      watchVariable('item', itemName);

      // Ensure that $digest calls on our scope trigger $digest on newScope.
      connectScopes();

      // Link the element against newScope.
      transclude(newScope, function(clone) {
        element.after(clone);
      });

      /**
       * Creates a watcher for variables that are copied from the parent scope
       * @param variable
       * @param alias
       */
      function watchVariable(variable, alias) {
        newScope[alias] = scope[variable];

        scope.$watch(variable, function(value) {
          $mdUtil.nextTick(function() {
            newScope[alias] = value;
          });
        });
      }

      /**
       * Creates watchers on scope and newScope that ensure that for any
       * $digest of scope, newScope is also $digested.
       */
      function connectScopes() {
        var scopeDigesting = false;
        var newScopeDigesting = false;

        scope.$watch(function() {
          if (newScopeDigesting || scopeDigesting) {
            return;
          }

          scopeDigesting = true;
          scope.$$postDigest(function() {
            if (!newScopeDigesting) {
              newScope.$digest();
            }

            scopeDigesting = newScopeDigesting = false;
          });
        });

        newScope.$watch(function() {
          newScopeDigesting = true;
        });
      }
    };
  }
}
})();
(function(){
"use strict";


MdHighlightCtrl.$inject = ["$scope", "$element", "$attrs"];angular
    .module('material.components.autocomplete')
    .controller('MdHighlightCtrl', MdHighlightCtrl);

function MdHighlightCtrl ($scope, $element, $attrs) {
  this.$scope = $scope;
  this.$element = $element;
  this.$attrs = $attrs;

  // Cache the Regex to avoid rebuilding each time.
  this.regex = null;
}

MdHighlightCtrl.prototype.init = function(unsafeTermFn, unsafeContentFn) {

  this.flags = this.$attrs.mdHighlightFlags || '';

  this.unregisterFn = this.$scope.$watch(function($scope) {
    return {
      term: unsafeTermFn($scope),
      contentText: unsafeContentFn($scope)
    };
  }.bind(this), this.onRender.bind(this), true);

  this.$element.on('$destroy', this.unregisterFn);
};

/**
 * Triggered once a new change has been recognized and the highlighted
 * text needs to be updated.
 */
MdHighlightCtrl.prototype.onRender = function(state, prevState) {

  var contentText = state.contentText;

  /* Update the regex if it's outdated, because we don't want to rebuilt it constantly. */
  if (this.regex === null || state.term !== prevState.term) {
    this.regex = this.createRegex(state.term, this.flags);
  }

  /* If a term is available apply the regex to the content */
  if (state.term) {
    this.applyRegex(contentText);
  } else {
    this.$element.text(contentText);
  }

};

/**
 * Decomposes the specified text into different tokens (whether match or not).
 * Breaking down the string guarantees proper XSS protection due to the native browser
 * escaping of unsafe text.
 */
MdHighlightCtrl.prototype.applyRegex = function(text) {
  var tokens = this.resolveTokens(text);

  this.$element.empty();

  tokens.forEach(function (token) {

    if (token.isMatch) {
      var tokenEl = angular.element('<span class="highlight">').text(token.text);

      this.$element.append(tokenEl);
    } else {
      this.$element.append(document.createTextNode(token));
    }

  }.bind(this));

};

  /**
 * Decomposes the specified text into different tokens by running the regex against the text.
 */
MdHighlightCtrl.prototype.resolveTokens = function(string) {
  var tokens = [];
  var lastIndex = 0;

  // Use replace here, because it supports global and single regular expressions at same time.
  string.replace(this.regex, function(match, index) {
    appendToken(lastIndex, index);

    tokens.push({
      text: match,
      isMatch: true
    });

    lastIndex = index + match.length;
  });

  // Append the missing text as a token.
  appendToken(lastIndex);

  return tokens;

  function appendToken(from, to) {
    var targetText = string.slice(from, to);
    targetText && tokens.push(targetText);
  }
};

/** Creates a regex for the specified text with the given flags. */
MdHighlightCtrl.prototype.createRegex = function(term, flags) {
  var startFlag = '', endFlag = '';
  var regexTerm = this.sanitizeRegex(term);

  if (flags.indexOf('^') >= 0) startFlag = '^';
  if (flags.indexOf('$') >= 0) endFlag = '$';

  return new RegExp(startFlag + regexTerm + endFlag, flags.replace(/[$\^]/g, ''));
};

/** Sanitizes a regex by removing all common RegExp identifiers */
MdHighlightCtrl.prototype.sanitizeRegex = function(term) {
  return term && term.toString().replace(/[\\\^\$\*\+\?\.\(\)\|\{}\[\]]/g, '\\$&');
};

})();
(function(){
"use strict";


MdHighlight.$inject = ["$interpolate", "$parse"];angular
    .module('material.components.autocomplete')
    .directive('mdHighlightText', MdHighlight);

/**
 * @ngdoc directive
 * @name mdHighlightText
 * @module material.components.autocomplete
 *
 * @description
 * The `md-highlight-text` directive allows you to specify text that should be highlighted within
 *     an element.  Highlighted text will be wrapped in `<span class="highlight"></span>` which can
 *     be styled through CSS.  Please note that child elements may not be used with this directive.
 *
 * @param {string} md-highlight-text A model to be searched for
 * @param {string=} md-highlight-flags A list of flags (loosely based on JavaScript RexExp flags).
 * #### **Supported flags**:
 * - `g`: Find all matches within the provided text
 * - `i`: Ignore case when searching for matches
 * - `$`: Only match if the text ends with the search term
 * - `^`: Only match if the text begins with the search term
 *
 * @usage
 * <hljs lang="html">
 * <input placeholder="Enter a search term..." ng-model="searchTerm" type="text" />
 * <ul>
 *   <li ng-repeat="result in results" md-highlight-text="searchTerm">
 *     {{result.text}}
 *   </li>
 * </ul>
 * </hljs>
 */

function MdHighlight ($interpolate, $parse) {
  return {
    terminal: true,
    controller: 'MdHighlightCtrl',
    compile: function mdHighlightCompile(tElement, tAttr) {
      var termExpr = $parse(tAttr.mdHighlightText);
      var unsafeContentExpr = $interpolate(tElement.html());

      return function mdHighlightLink(scope, element, attr, ctrl) {
        ctrl.init(termExpr, unsafeContentExpr);
      };
    }
  };
}

})();
(function(){
"use strict";


MdChipCtrl.$inject = ["$scope", "$element", "$mdConstant", "$timeout", "$mdUtil"];angular
  .module('material.components.chips')
  .controller('MdChipCtrl', MdChipCtrl);

/**
 * Controller for the MdChip component. Responsible for handling keyboard
 * events and editting the chip if needed.
 *
 * @param $scope
 * @param $element
 * @param $mdConstant
 * @param $timeout
 * @param $mdUtil
 * @constructor
 */
function MdChipCtrl ($scope, $element, $mdConstant, $timeout, $mdUtil) {
  /**
   * @type {$scope}
   */
  this.$scope = $scope;

  /**
   * @type {$element}
   */
  this.$element = $element;

  /**
   * @type {$mdConstant}
   */
  this.$mdConstant = $mdConstant;

  /**
   * @type {$timeout}
   */
  this.$timeout = $timeout;

  /**
   * @type {$mdUtil}
   */
  this.$mdUtil = $mdUtil;

  /**
   * @type {boolean}
   */
  this.isEditting = false;

  /**
   * @type {MdChipsCtrl}
   */
  this.parentController = undefined;

  /**
   * @type {boolean}
   */
  this.enableChipEdit = false;
}


/**
 * @param {MdChipsCtrl} controller
 */
MdChipCtrl.prototype.init = function(controller) {
  this.parentController = controller;
  this.enableChipEdit = this.parentController.enableChipEdit;

  if (this.enableChipEdit) {
    this.$element.on('keydown', this.chipKeyDown.bind(this));
    this.$element.on('mousedown', this.chipMouseDown.bind(this));
    this.getChipContent().addClass('_md-chip-content-edit-is-enabled');
  }
};


/**
 * @return {Object}
 */
MdChipCtrl.prototype.getChipContent = function() {
  var chipContents = this.$element[0].getElementsByClassName('md-chip-content');
  return angular.element(chipContents[0]);
};


/**
 * @return {Object}
 */
MdChipCtrl.prototype.getContentElement = function() {
  return angular.element(this.getChipContent().children()[0]);
};


/**
 * @return {number}
 */
MdChipCtrl.prototype.getChipIndex = function() {
  return parseInt(this.$element.attr('index'));
};


/**
 * Presents an input element to edit the contents of the chip.
 */
MdChipCtrl.prototype.goOutOfEditMode = function() {
  if (!this.isEditting) return;

  this.isEditting = false;
  this.$element.removeClass('_md-chip-editing');
  this.getChipContent()[0].contentEditable = 'false';
  var chipIndex = this.getChipIndex();

  var content = this.getContentElement().text();
  if (content) {
    this.parentController.updateChipContents(
        chipIndex,
        this.getContentElement().text()
    );

    this.$mdUtil.nextTick(function() {
      if (this.parentController.selectedChip === chipIndex) {
        this.parentController.focusChip(chipIndex);
      }
    }.bind(this));
  } else {
    this.parentController.removeChipAndFocusInput(chipIndex);
  }
};


/**
 * Given an HTML element. Selects contents of it.
 * @param node
 */
MdChipCtrl.prototype.selectNodeContents = function(node) {
  var range, selection;
  if (document.body.createTextRange) {
    range = document.body.createTextRange();
    range.moveToElementText(node);
    range.select();
  } else if (window.getSelection) {
    selection = window.getSelection();
    range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};


/**
 * Presents an input element to edit the contents of the chip.
 */
MdChipCtrl.prototype.goInEditMode = function() {
  this.isEditting = true;
  this.$element.addClass('_md-chip-editing');
  this.getChipContent()[0].contentEditable = 'true';
  this.getChipContent().on('blur', function() {
    this.goOutOfEditMode();
  }.bind(this));

  this.selectNodeContents(this.getChipContent()[0]);
};


/**
 * Handles the keydown event on the chip element. If enable-chip-edit attribute is
 * set to true, space or enter keys can trigger going into edit mode. Enter can also
 * trigger submitting if the chip is already being edited.
 * @param event
 */
MdChipCtrl.prototype.chipKeyDown = function(event) {
  if (!this.isEditting &&
    (event.keyCode === this.$mdConstant.KEY_CODE.ENTER ||
    event.keyCode === this.$mdConstant.KEY_CODE.SPACE)) {
    event.preventDefault();
    this.goInEditMode();
  } else if (this.isEditting &&
    event.keyCode === this.$mdConstant.KEY_CODE.ENTER) {
    event.preventDefault();
    this.goOutOfEditMode();
  }
};


/**
 * Handles the double click event
 */
MdChipCtrl.prototype.chipMouseDown = function() {
  if(this.getChipIndex() == this.parentController.selectedChip &&
    this.enableChipEdit &&
    !this.isEditting) {
    this.goInEditMode();
  }
};

})();
(function(){
"use strict";


MdChip.$inject = ["$mdTheming", "$mdUtil", "$compile", "$timeout"];angular
  .module('material.components.chips')
  .directive('mdChip', MdChip);

/**
 * @ngdoc directive
 * @name mdChip
 * @module material.components.chips
 *
 * @description
 * `<md-chip>` is a component used within `<md-chips>` and is responsible for rendering individual
 * chips.
 *
 *
 * @usage
 * <hljs lang="html">
 *   <md-chip>{{$chip}}</md-chip>
 * </hljs>
 *
 */

// This hint text is hidden within a chip but used by screen readers to
// inform the user how they can interact with a chip.
var DELETE_HINT_TEMPLATE = '\
    <span ng-if="!$mdChipsCtrl.readonly" class="md-visually-hidden">\
      {{$mdChipsCtrl.deleteHint}}\
    </span>';

/**
 * MDChip Directive Definition
 *
 * @param $mdTheming
 * @param $mdUtil
 * @ngInject
 */
function MdChip($mdTheming, $mdUtil, $compile, $timeout) {
  var deleteHintTemplate = $mdUtil.processTemplate(DELETE_HINT_TEMPLATE);

  return {
    restrict: 'E',
    require: ['^?mdChips', 'mdChip'],
    link: postLink,
    controller: 'MdChipCtrl'
  };

  function postLink(scope, element, attr, ctrls) {
    var chipsController = ctrls.shift();
    var chipController = ctrls.shift();
    var chipContentElement = angular.element(element[0].querySelector('.md-chip-content'));

    $mdTheming(element);

    if (chipsController) {
      chipController.init(chipsController);

      // Append our delete hint to the div.md-chip-content (which does not exist at compile time)
      chipContentElement.append($compile(deleteHintTemplate)(scope));

      // When a chip is blurred, make sure to unset (or reset) the selected chip so that tabbing
      // through elements works properly
      chipContentElement.on('blur', function() {
        chipsController.resetSelectedChip();
        chipsController.$scope.$applyAsync();
      });
    }

    // Use $timeout to ensure we run AFTER the element has been added to the DOM so we can focus it.
    $timeout(function() {
      if (!chipsController) {
        return;
      }

      if (chipsController.shouldFocusLastChip) {
        chipsController.focusLastChipThenInput();
      }
    });
  }
}

})();
(function(){
"use strict";


MdChipRemove.$inject = ["$timeout"];angular
    .module('material.components.chips')
    .directive('mdChipRemove', MdChipRemove);

/**
 * @ngdoc directive
 * @name mdChipRemove
 * @restrict A
 * @module material.components.chips
 *
 * @description
 * Designates an element to be used as the delete button for a chip. <br/>
 * This element is passed as a child of the `md-chips` element.
 *
 * The designated button will be just appended to the chip and removes the given chip on click.<br/>
 * By default the button is not being styled by the `md-chips` component.
 *
 * @usage
 * <hljs lang="html">
 *   <md-chips>
 *     <button md-chip-remove="">
 *       <md-icon md-svg-icon="md-close"></md-icon>
 *     </button>
 *   </md-chips>
 * </hljs>
 */


/**
 * MdChipRemove Directive Definition.
 * 
 * @param $timeout
 * @returns {{restrict: string, require: string[], link: Function, scope: boolean}}
 * @constructor
 */
function MdChipRemove ($timeout) {
  return {
    restrict: 'A',
    require: '^mdChips',
    scope: false,
    link: postLink
  };

  function postLink(scope, element, attr, ctrl) {
    element.on('click', function(event) {
      scope.$apply(function() {
        ctrl.removeChip(scope.$$replacedScope.$index);
      });
    });

    // Child elements aren't available until after a $timeout tick as they are hidden by an
    // `ng-if`. see http://goo.gl/zIWfuw
    $timeout(function() {
      element.attr({ tabindex: -1, 'aria-hidden': true });
      element.find('button').attr('tabindex', '-1');
    });
  }
}

})();
(function(){
"use strict";


MdChipTransclude.$inject = ["$compile"];angular
    .module('material.components.chips')
    .directive('mdChipTransclude', MdChipTransclude);

function MdChipTransclude ($compile) {
  return {
    restrict: 'EA',
    terminal: true,
    link: link,
    scope: false
  };
  function link (scope, element, attr) {
    var ctrl = scope.$parent.$mdChipsCtrl,
        newScope = ctrl.parent.$new(false, ctrl.parent);
    newScope.$$replacedScope = scope;
    newScope.$chip = scope.$chip;
    newScope.$index = scope.$index;
    newScope.$mdChipsCtrl = ctrl;

    var newHtml = ctrl.$scope.$eval(attr.mdChipTransclude);

    element.html(newHtml);
    $compile(element.contents())(newScope);
  }
}

})();
(function(){
"use strict";

/**
 * The default chip append delay.
 *
 * @type {number}
 */
MdChipsCtrl.$inject = ["$scope", "$attrs", "$mdConstant", "$log", "$element", "$timeout", "$mdUtil"];
var DEFAULT_CHIP_APPEND_DELAY = 300;

angular
    .module('material.components.chips')
    .controller('MdChipsCtrl', MdChipsCtrl);

/**
 * Controller for the MdChips component. Responsible for adding to and
 * removing from the list of chips, marking chips as selected, and binding to
 * the models of various input components.
 *
 * @param $scope
 * @param $attrs
 * @param $mdConstant
 * @param $log
 * @param $element
 * @param $timeout
 * @param $mdUtil
 * @constructor
 */
function MdChipsCtrl ($scope, $attrs, $mdConstant, $log, $element, $timeout, $mdUtil) {
  /** @type {$timeout} **/
  this.$timeout = $timeout;

  /** @type {Object} */
  this.$mdConstant = $mdConstant;

  /** @type {angular.$scope} */
  this.$scope = $scope;

  /** @type {angular.$scope} */
  this.parent = $scope.$parent;

  /** @type {$mdUtil} */
  this.$mdUtil = $mdUtil;

  /** @type {$log} */
  this.$log = $log;

  /** @type {$element} */
  this.$element = $element;

  /** @type {$attrs} */
  this.$attrs = $attrs;

  /** @type {angular.NgModelController} */
  this.ngModelCtrl = null;

  /** @type {angular.NgModelController} */
  this.userInputNgModelCtrl = null;

  /** @type {MdAutocompleteCtrl} */
  this.autocompleteCtrl = null;

  /** @type {Element} */
  this.userInputElement = null;

  /** @type {Array.<Object>} */
  this.items = [];

  /** @type {number} */
  this.selectedChip = -1;

  /** @type {string} */
  this.enableChipEdit = $mdUtil.parseAttributeBoolean($attrs.mdEnableChipEdit);

  /** @type {string} */
  this.addOnBlur = $mdUtil.parseAttributeBoolean($attrs.mdAddOnBlur);

  /**
   * The text to be used as the aria-label for the input.
   * @type {string}
   */
  this.inputAriaLabel = 'Chips input.';

  /**
   * Hidden hint text to describe the chips container. Used to give context to screen readers when
   * the chips are readonly and the input cannot be selected.
   *
   * @type {string}
   */
  this.containerHint = 'Chips container. Use arrow keys to select chips.';

  /**
   * Hidden hint text for how to delete a chip. Used to give context to screen readers.
   * @type {string}
   */
  this.deleteHint = 'Press delete to remove this chip.';

  /**
   * Hidden label for the delete button. Used to give context to screen readers.
   * @type {string}
   */
  this.deleteButtonLabel = 'Remove';

  /**
   * Model used by the input element.
   * @type {string}
   */
  this.chipBuffer = '';

  /**
   * Whether to use the transformChip expression to transform the chip buffer
   * before appending it to the list.
   * @type {boolean}
   */
  this.useTransformChip = false;

  /**
   * Whether to use the onAdd expression to notify of chip additions.
   * @type {boolean}
   */
  this.useOnAdd = false;

  /**
   * Whether to use the onRemove expression to notify of chip removals.
   * @type {boolean}
   */
  this.useOnRemove = false;

  /**
   * The ID of the chips wrapper which is used to build unique IDs for the chips and the aria-owns
   * attribute.
   *
   * Defaults to '_md-chips-wrapper-' plus a unique number.
   *
   * @type {string}
   */
  this.wrapperId = '';

  /**
   * Array of unique numbers which will be auto-generated any time the items change, and is used to
   * create unique IDs for the aria-owns attribute.
   *
   * @type {Array}
   */
  this.contentIds = [];

  /**
   * The index of the chip that should have it's tabindex property set to 0 so it is selectable
   * via the keyboard.
   *
   * @type {int}
   */
  this.ariaTabIndex = null;

  /**
   * After appending a chip, the chip will be focused for this number of milliseconds before the
   * input is refocused.
   *
   * **Note:** This is **required** for compatibility with certain screen readers in order for
   * them to properly allow keyboard access.
   *
   * @type {number}
   */
  this.chipAppendDelay = DEFAULT_CHIP_APPEND_DELAY;

  this.init();
}

/**
 * Initializes variables and sets up watchers
 */
MdChipsCtrl.prototype.init = function() {
  var ctrl = this;

  // Set the wrapper ID
  ctrl.wrapperId = '_md-chips-wrapper-' + ctrl.$mdUtil.nextUid();

  // Setup a watcher which manages the role and aria-owns attributes
  ctrl.$scope.$watchCollection('$mdChipsCtrl.items', function() {
    // Make sure our input and wrapper have the correct ARIA attributes
    ctrl.setupInputAria();
    ctrl.setupWrapperAria();
  });

  ctrl.$attrs.$observe('mdChipAppendDelay', function(newValue) {
    ctrl.chipAppendDelay = parseInt(newValue) || DEFAULT_CHIP_APPEND_DELAY;
  });
};

/**
 * If we have an input, ensure it has the appropriate ARIA attributes.
 */
MdChipsCtrl.prototype.setupInputAria = function() {
  var input = this.$element.find('input');

  // If we have no input, just return
  if (!input) {
    return;
  }

  input.attr('role', 'textbox');
  input.attr('aria-multiline', true);
};

/**
 * Ensure our wrapper has the appropriate ARIA attributes.
 */
MdChipsCtrl.prototype.setupWrapperAria = function() {
  var ctrl = this,
      wrapper = this.$element.find('md-chips-wrap');

  if (this.items && this.items.length) {
    // Dynamically add the listbox role on every change because it must be removed when there are
    // no items.
    wrapper.attr('role', 'listbox');

    // Generate some random (but unique) IDs for each chip
    this.contentIds = this.items.map(function() {
      return ctrl.wrapperId + '-chip-' + ctrl.$mdUtil.nextUid();
    });

    // Use the contentIDs above to generate the aria-owns attribute
    wrapper.attr('aria-owns', this.contentIds.join(' '));
  } else {
    // If we have no items, then the role and aria-owns attributes MUST be removed
    wrapper.removeAttr('role');
    wrapper.removeAttr('aria-owns');
  }
};

/**
 * Handles the keydown event on the input element: by default <enter> appends
 * the buffer to the chip list, while backspace removes the last chip in the
 * list if the current buffer is empty.
 * @param event
 */
MdChipsCtrl.prototype.inputKeydown = function(event) {
  var chipBuffer = this.getChipBuffer();

  // If we have an autocomplete, and it handled the event, we have nothing to do
  if (this.autocompleteCtrl && event.isDefaultPrevented && event.isDefaultPrevented()) {
    return;
  }

  if (event.keyCode === this.$mdConstant.KEY_CODE.BACKSPACE) {
    // Only select and focus the previous chip, if the current caret position of the
    // input element is at the beginning.
    if (this.getCursorPosition(event.target) !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.items.length) {
      this.selectAndFocusChipSafe(this.items.length - 1);
    }

    return;
  }

  // By default <enter> appends the buffer to the chip list.
  if (!this.separatorKeys || this.separatorKeys.length < 1) {
    this.separatorKeys = [this.$mdConstant.KEY_CODE.ENTER];
  }

  // Support additional separator key codes in an array of `md-separator-keys`.
  if (this.separatorKeys.indexOf(event.keyCode) !== -1) {
    if ((this.autocompleteCtrl && this.requireMatch) || !chipBuffer) return;
    event.preventDefault();

    // Only append the chip and reset the chip buffer if the max chips limit isn't reached.
    if (this.hasMaxChipsReached()) return;

    this.appendChip(chipBuffer.trim());
    this.resetChipBuffer();

    return false;
  }
};

/**
 * Returns the cursor position of the specified input element.
 * @param element HTMLInputElement
 * @returns {Number} Cursor Position of the input.
 */
MdChipsCtrl.prototype.getCursorPosition = function(element) {
  /*
   * Figure out whether the current input for the chips buffer is valid for using
   * the selectionStart / end property to retrieve the cursor position.
   * Some browsers do not allow the use of those attributes, on different input types.
   */
  try {
    if (element.selectionStart === element.selectionEnd) {
      return element.selectionStart;
    }
  } catch (e) {
    if (!element.value) {
      return 0;
    }
  }
};


/**
 * Updates the content of the chip at given index
 * @param chipIndex
 * @param chipContents
 */
MdChipsCtrl.prototype.updateChipContents = function(chipIndex, chipContents){
  if(chipIndex >= 0 && chipIndex < this.items.length) {
    this.items[chipIndex] = chipContents;
    this.ngModelCtrl.$setDirty();
  }
};


/**
 * Returns true if a chip is currently being edited. False otherwise.
 * @return {boolean}
 */
MdChipsCtrl.prototype.isEditingChip = function() {
  return !!this.$element[0].querySelector('._md-chip-editing');
};


MdChipsCtrl.prototype.isRemovable = function() {
  // Return false if we have static chips
  if (!this.ngModelCtrl) {
    return false;
  }

  return this.readonly ? this.removable :
         angular.isDefined(this.removable) ? this.removable : true;
};

/**
 * Handles the keydown event on the chip elements: backspace removes the selected chip, arrow
 * keys switch which chips is active
 * @param event
 */
MdChipsCtrl.prototype.chipKeydown = function (event) {
  if (this.getChipBuffer()) return;
  if (this.isEditingChip()) return;

  switch (event.keyCode) {
    case this.$mdConstant.KEY_CODE.BACKSPACE:
    case this.$mdConstant.KEY_CODE.DELETE:
      if (this.selectedChip < 0) return;
      event.preventDefault();
      // Cancel the delete action only after the event cancel. Otherwise the page will go back.
      if (!this.isRemovable()) return;
      this.removeAndSelectAdjacentChip(this.selectedChip);
      break;
    case this.$mdConstant.KEY_CODE.LEFT_ARROW:
      event.preventDefault();
      // By default, allow selection of -1 which will focus the input; if we're readonly, don't go
      // below 0
      if (this.selectedChip < 0 || (this.readonly && this.selectedChip == 0)) {
        this.selectedChip = this.items.length;
      }
      if (this.items.length) this.selectAndFocusChipSafe(this.selectedChip - 1);
      break;
    case this.$mdConstant.KEY_CODE.RIGHT_ARROW:
      event.preventDefault();
      this.selectAndFocusChipSafe(this.selectedChip + 1);
      break;
    case this.$mdConstant.KEY_CODE.ESCAPE:
    case this.$mdConstant.KEY_CODE.TAB:
      if (this.selectedChip < 0) return;
      event.preventDefault();
      this.onFocus();
      break;
  }
};

/**
 * Get the input's placeholder - uses `placeholder` when list is empty and `secondary-placeholder`
 * when the list is non-empty. If `secondary-placeholder` is not provided, `placeholder` is used
 * always.
 */
MdChipsCtrl.prototype.getPlaceholder = function() {
  // Allow `secondary-placeholder` to be blank.
  var useSecondary = (this.items && this.items.length &&
      (this.secondaryPlaceholder == '' || this.secondaryPlaceholder));
  return useSecondary ? this.secondaryPlaceholder : this.placeholder;
};

/**
 * Removes chip at {@code index} and selects the adjacent chip.
 * @param index
 */
MdChipsCtrl.prototype.removeAndSelectAdjacentChip = function(index) {
  var self = this;
  var selIndex = self.getAdjacentChipIndex(index);
  var wrap = this.$element[0].querySelector('md-chips-wrap');
  var chip = this.$element[0].querySelector('md-chip[index="' + index + '"]');

  self.removeChip(index);

  // The dobule-timeout is currently necessary to ensure that the DOM has finalized and the select()
  // will find the proper chip since the selection is index-based.
  //
  // TODO: Investigate calling from within chip $scope.$on('$destroy') to reduce/remove timeouts
  self.$timeout(function() {
    self.$timeout(function() {
      self.selectAndFocusChipSafe(selIndex);
    });
  });
};

/**
 * Sets the selected chip index to -1.
 */
MdChipsCtrl.prototype.resetSelectedChip = function() {
  this.selectedChip = -1;
  this.ariaTabIndex = null;
};

/**
 * Gets the index of an adjacent chip to select after deletion. Adjacency is
 * determined as the next chip in the list, unless the target chip is the
 * last in the list, then it is the chip immediately preceding the target. If
 * there is only one item in the list, -1 is returned (select none).
 * The number returned is the index to select AFTER the target has been
 * removed.
 * If the current chip is not selected, then -1 is returned to select none.
 */
MdChipsCtrl.prototype.getAdjacentChipIndex = function(index) {
  var len = this.items.length - 1;
  return (len == 0) ? -1 :
      (index == len) ? index -1 : index;
};

/**
 * Append the contents of the buffer to the chip list. This method will first
 * call out to the md-transform-chip method, if provided.
 *
 * @param newChip
 */
MdChipsCtrl.prototype.appendChip = function(newChip) {
  this.shouldFocusLastChip = true;
  if (this.useTransformChip && this.transformChip) {
    var transformedChip = this.transformChip({'$chip': newChip});

    // Check to make sure the chip is defined before assigning it, otherwise, we'll just assume
    // they want the string version.
    if (angular.isDefined(transformedChip)) {
      newChip = transformedChip;
    }
  }

  // If items contains an identical object to newChip, do not append
  if (angular.isObject(newChip)){
    var identical = this.items.some(function(item){
      return angular.equals(newChip, item);
    });
    if (identical) return;
  }

  // Check for a null (but not undefined), or existing chip and cancel appending
  if (newChip == null || this.items.indexOf(newChip) + 1) return;

  // Append the new chip onto our list
  var length = this.items.push(newChip);
  var index = length - 1;

  // Update model validation
  this.ngModelCtrl.$setDirty();
  this.validateModel();

  // If they provide the md-on-add attribute, notify them of the chip addition
  if (this.useOnAdd && this.onAdd) {
    this.onAdd({ '$chip': newChip, '$index': index });
  }
};

/**
 * Sets whether to use the md-transform-chip expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code transformChip}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useTransformChipExpression = function() {
  this.useTransformChip = true;
};

/**
 * Sets whether to use the md-on-add expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onAdd}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnAddExpression = function() {
  this.useOnAdd = true;
};

/**
 * Sets whether to use the md-on-remove expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onRemove}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnRemoveExpression = function() {
  this.useOnRemove = true;
};

/*
 * Sets whether to use the md-on-select expression. This expression is
 * bound to scope and controller in {@code MdChipsDirective} as
 * {@code onSelect}. Due to the nature of directive scope bindings, the
 * controller cannot know on its own/from the scope whether an expression was
 * actually provided.
 */
MdChipsCtrl.prototype.useOnSelectExpression = function() {
  this.useOnSelect = true;
};

/**
 * Gets the input buffer. The input buffer can be the model bound to the
 * default input item {@code this.chipBuffer}, the {@code selectedItem}
 * model of an {@code md-autocomplete}, or, through some magic, the model
 * bound to any inpput or text area element found within a
 * {@code md-input-container} element.
 * @return {string}
 */
MdChipsCtrl.prototype.getChipBuffer = function() {
  var chipBuffer =  !this.userInputElement ? this.chipBuffer :
                     this.userInputNgModelCtrl ? this.userInputNgModelCtrl.$viewValue :
                     this.userInputElement[0].value;

  // Ensure that the chip buffer is always a string. For example, the input element buffer might be falsy.
  return angular.isString(chipBuffer) ? chipBuffer : '';
};

/**
 * Resets the input buffer for either the internal input or user provided input element.
 */
MdChipsCtrl.prototype.resetChipBuffer = function() {
  if (this.userInputElement) {
    if (this.userInputNgModelCtrl) {
      this.userInputNgModelCtrl.$setViewValue('');
      this.userInputNgModelCtrl.$render();
    } else {
      this.userInputElement[0].value = '';
    }
  } else {
    this.chipBuffer = '';
  }
};

MdChipsCtrl.prototype.hasMaxChipsReached = function() {
  if (angular.isString(this.maxChips)) this.maxChips = parseInt(this.maxChips, 10) || 0;

  return this.maxChips > 0 && this.items.length >= this.maxChips;
};

/**
 * Updates the validity properties for the ngModel.
 */
MdChipsCtrl.prototype.validateModel = function() {
  this.ngModelCtrl.$setValidity('md-max-chips', !this.hasMaxChipsReached());
};

/**
 * Removes the chip at the given index.
 * @param index
 */
MdChipsCtrl.prototype.removeChip = function(index) {
  var removed = this.items.splice(index, 1);

  // Update model validation
  this.ngModelCtrl.$setDirty();
  this.validateModel();

  if (removed && removed.length && this.useOnRemove && this.onRemove) {
    this.onRemove({ '$chip': removed[0], '$index': index });
  }
};

MdChipsCtrl.prototype.removeChipAndFocusInput = function (index) {
  this.removeChip(index);

  if (this.autocompleteCtrl) {
    // Always hide the autocomplete dropdown before focusing the autocomplete input.
    // Wait for the input to move horizontally, because the chip was removed.
    // This can lead to an incorrect dropdown position.
    this.autocompleteCtrl.hidden = true;
    this.$mdUtil.nextTick(this.onFocus.bind(this));
  } else {
    this.onFocus();
  }

};
/**
 * Selects the chip at `index`,
 * @param index
 */
MdChipsCtrl.prototype.selectAndFocusChipSafe = function(index) {
  // If we have no chips, or are asked to select a chip before the first, just focus the input
  if (!this.items.length || index === -1) {
    return this.focusInput();
  }

  // If we are asked to select a chip greater than the number of chips...
  if (index >= this.items.length) {
    if (this.readonly) {
      // If we are readonly, jump back to the start (because we have no input)
      index = 0;
    } else {
      // If we are not readonly, we should attempt to focus the input
      return this.onFocus();
    }
  }

  index = Math.max(index, 0);
  index = Math.min(index, this.items.length - 1);

  this.selectChip(index);
  this.focusChip(index);
};

MdChipsCtrl.prototype.focusLastChipThenInput = function() {
  var ctrl = this;

  ctrl.shouldFocusLastChip = false;

  ctrl.focusChip(this.items.length - 1);

  ctrl.$timeout(function() {
    ctrl.focusInput();
  }, ctrl.chipAppendDelay);
};

MdChipsCtrl.prototype.focusInput = function() {
  this.selectChip(-1);
  this.onFocus();
};

/**
 * Marks the chip at the given index as selected.
 * @param index
 */
MdChipsCtrl.prototype.selectChip = function(index) {
  if (index >= -1 && index <= this.items.length) {
    this.selectedChip = index;

    // Fire the onSelect if provided
    if (this.useOnSelect && this.onSelect) {
      this.onSelect({'$chip': this.items[index] });
    }
  } else {
    this.$log.warn('Selected Chip index out of bounds; ignoring.');
  }
};

/**
 * Selects the chip at `index` and gives it focus.
 * @param index
 */
MdChipsCtrl.prototype.selectAndFocusChip = function(index) {
  this.selectChip(index);
  if (index != -1) {
    this.focusChip(index);
  }
};

/**
 * Call `focus()` on the chip at `index`
 */
MdChipsCtrl.prototype.focusChip = function(index) {
  var chipContent = this.$element[0].querySelector('md-chip[index="' + index + '"] .md-chip-content');

  this.ariaTabIndex = index;

  chipContent.focus();
};

/**
 * Configures the required interactions with the ngModel Controller.
 * Specifically, set {@code this.items} to the {@code NgModelCtrl#$viewVale}.
 * @param ngModelCtrl
 */
MdChipsCtrl.prototype.configureNgModel = function(ngModelCtrl) {
  this.ngModelCtrl = ngModelCtrl;

  var self = this;
  ngModelCtrl.$render = function() {
    // model is updated. do something.
    self.items = self.ngModelCtrl.$viewValue;
  };
};

MdChipsCtrl.prototype.onFocus = function () {
  var input = this.$element[0].querySelector('input');
  input && input.focus();
  this.resetSelectedChip();
};

MdChipsCtrl.prototype.onInputFocus = function () {
  this.inputHasFocus = true;

  // Make sure we have the appropriate ARIA attributes
  this.setupInputAria();

  // Make sure we don't have any chips selected
  this.resetSelectedChip();
};

MdChipsCtrl.prototype.onInputBlur = function () {
  this.inputHasFocus = false;

  if (this.shouldAddOnBlur()) {
    this.appendChip(this.getChipBuffer().trim());
    this.resetChipBuffer();
  }
};

/**
 * Configure event bindings on a user-provided input element.
 * @param inputElement
 */
MdChipsCtrl.prototype.configureUserInput = function(inputElement) {
  this.userInputElement = inputElement;

  // Find the NgModelCtrl for the input element
  var ngModelCtrl = inputElement.controller('ngModel');
  // `.controller` will look in the parent as well.
  if (ngModelCtrl != this.ngModelCtrl) {
    this.userInputNgModelCtrl = ngModelCtrl;
  }

  var scope = this.$scope;
  var ctrl = this;

  // Run all of the events using evalAsync because a focus may fire a blur in the same digest loop
  var scopeApplyFn = function(event, fn) {
    scope.$evalAsync(angular.bind(ctrl, fn, event));
  };

  // Bind to keydown and focus events of input
  inputElement
      .attr({ tabindex: 0 })
      .on('keydown', function(event) { scopeApplyFn(event, ctrl.inputKeydown) })
      .on('focus', function(event) { scopeApplyFn(event, ctrl.onInputFocus) })
      .on('blur', function(event) { scopeApplyFn(event, ctrl.onInputBlur) })
};

MdChipsCtrl.prototype.configureAutocomplete = function(ctrl) {
  if (ctrl) {
    this.autocompleteCtrl = ctrl;

    ctrl.registerSelectedItemWatcher(angular.bind(this, function (item) {
      if (item) {
        // Only append the chip and reset the chip buffer if the max chips limit isn't reached.
        if (this.hasMaxChipsReached()) return;

        this.appendChip(item);
        this.resetChipBuffer();
      }
    }));

    this.$element.find('input')
        .on('focus',angular.bind(this, this.onInputFocus) )
        .on('blur', angular.bind(this, this.onInputBlur) );
  }
};

/**
 * Whether the current chip buffer should be added on input blur or not.
 * @returns {boolean}
 */
MdChipsCtrl.prototype.shouldAddOnBlur = function() {

  // Update the custom ngModel validators from the chips component.
  this.validateModel();

  var chipBuffer = this.getChipBuffer().trim();
  var isModelValid = this.ngModelCtrl.$valid;
  var isAutocompleteShowing = this.autocompleteCtrl && !this.autocompleteCtrl.hidden;

  if (this.userInputNgModelCtrl) {
    isModelValid = isModelValid && this.userInputNgModelCtrl.$valid;
  }

  return this.addOnBlur && !this.requireMatch && chipBuffer && isModelValid && !isAutocompleteShowing;
};

MdChipsCtrl.prototype.hasFocus = function () {
  return this.inputHasFocus || this.selectedChip >= 0;
};

MdChipsCtrl.prototype.contentIdFor = function(index) {
  return this.contentIds[index];
};

})();
(function(){
"use strict";

  
  MdChips.$inject = ["$mdTheming", "$mdUtil", "$compile", "$log", "$timeout", "$$mdSvgRegistry"];angular
      .module('material.components.chips')
      .directive('mdChips', MdChips);

  /**
   * @ngdoc directive
   * @name mdChips
   * @module material.components.chips
   *
   * @description
   * `<md-chips>` is an input component for building lists of strings or objects. The list items are
   * displayed as 'chips'. This component can make use of an `<input>` element or an 
   * `<md-autocomplete>` element.
   *
   * ### Custom templates
   * A custom template may be provided to render the content of each chip. This is achieved by
   * specifying an `<md-chip-template>` element containing the custom content as a child of
   * `<md-chips>`.
   *
   * Note: Any attributes on
   * `<md-chip-template>` will be dropped as only the innerHTML is used for the chip template. The
   * variables `$chip` and `$index` are available in the scope of `<md-chip-template>`, representing
   * the chip object and its index in the list of chips, respectively.
   * To override the chip delete control, include an element (ideally a button) with the attribute
   * `md-chip-remove`. A click listener to remove the chip will be added automatically. The element
   * is also placed as a sibling to the chip content (on which there are also click listeners) to
   * avoid a nested ng-click situation.
   *
   * <!-- Note: We no longer want to include this in the site docs; but it should remain here for
   * future developers and those looking at the documentation.
   *
   * <h3> Pending Features </h3>
   * <ul style="padding-left:20px;">
   *
   *   <ul>Style
   *     <li>Colours for hover, press states (ripple?).</li>
   *   </ul>
   *
   *   <ul>Validation
   *     <li>allow a validation callback</li>
   *     <li>highlighting style for invalid chips</li>
   *   </ul>
   *
   *   <ul>Item mutation
   *     <li>Support `
   *       <md-chip-edit>` template, show/hide the edit element on tap/click? double tap/double
   *       click?
   *     </li>
   *   </ul>
   *
   *   <ul>Truncation and Disambiguation (?)
   *     <li>Truncate chip text where possible, but do not truncate entries such that two are
   *     indistinguishable.</li>
   *   </ul>
   *
   *   <ul>Drag and Drop
   *     <li>Drag and drop chips between related `<md-chips>` elements.
   *     </li>
   *   </ul>
   * </ul>
   *
   * //-->
   *
   * Sometimes developers want to limit the amount of possible chips.<br/>
   * You can specify the maximum amount of chips by using the following markup.
   *
   * <hljs lang="html">
   *   <md-chips
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       md-max-chips="5">
   *   </md-chips>
   * </hljs>
   *
   * In some cases, you have an autocomplete inside of the `md-chips`.<br/>
   * When the maximum amount of chips has been reached, you can also disable the autocomplete selection.<br/>
   * Here is an example markup.
   *
   * <hljs lang="html">
   *   <md-chips ng-model="myItems" md-max-chips="5">
   *     <md-autocomplete ng-hide="myItems.length > 5" ...></md-autocomplete>
   *   </md-chips>
   * </hljs>
   *
   * ### Accessibility
   *
   * The `md-chips` component supports keyboard and screen reader users since Version 1.1.2. In
   * order to achieve this, we modified the chips behavior to select newly appended chips for
   * `300ms` before re-focusing the input and allowing the user to type.
   *
   * For most users, this delay is small enough that it will not be noticeable but allows certain
   * screen readers to function properly (JAWS and NVDA in particular).
   *
   * We introduced a new `md-chip-append-delay` option to allow developers to better control this
   * behavior.
   *
   * Please refer to the documentation of this option (below) for more information.
   *
   * @param {string=|object=} ng-model A model to which the list of items will be bound.
   * @param {string=} placeholder Placeholder text that will be forwarded to the input.
   * @param {string=} secondary-placeholder Placeholder text that will be forwarded to the input,
   *    displayed when there is at least one item in the list
   * @param {boolean=} md-removable Enables or disables the deletion of chips through the
   *    removal icon or the Delete/Backspace key. Defaults to true.
   * @param {boolean=} readonly Disables list manipulation (deleting or adding list items), hiding
   *    the input and delete buttons. If no `ng-model` is provided, the chips will automatically be
   *    marked as readonly.<br/><br/>
   *    When `md-removable` is not defined, the `md-remove` behavior will be overwritten and disabled.
   * @param {string=} md-enable-chip-edit Set this to "true" to enable editing of chip contents. The user can 
   *    go into edit mode with pressing "space", "enter", or double clicking on the chip. Chip edit is only
   *    supported for chips with basic template.
   * @param {number=} md-max-chips The maximum number of chips allowed to add through user input.
   *    <br/><br/>The validation property `md-max-chips` can be used when the max chips
   *    amount is reached.
   * @param {boolean=} md-add-on-blur When set to true, remaining text inside of the input will
   *    be converted into a new chip on blur.
   * @param {expression} md-transform-chip An expression of form `myFunction($chip)` that when called
   *    expects one of the following return values:
   *    - an object representing the `$chip` input string
   *    - `undefined` to simply add the `$chip` input string, or
   *    - `null` to prevent the chip from being appended
   * @param {expression=} md-on-add An expression which will be called when a chip has been
   *    added.
   * @param {expression=} md-on-remove An expression which will be called when a chip has been
   *    removed.
   * @param {expression=} md-on-select An expression which will be called when a chip is selected.
   * @param {boolean} md-require-match If true, and the chips template contains an autocomplete,
   *    only allow selection of pre-defined chips (i.e. you cannot add new ones).
   * @param {string=} input-aria-label A string read by screen readers to identify the input.
   * @param {string=} container-hint A string read by screen readers informing users of how to
   *    navigate the chips. Used in readonly mode.
   * @param {string=} delete-hint A string read by screen readers instructing users that pressing
   *    the delete key will remove the chip.
   * @param {string=} delete-button-label A label for the delete button. Also hidden and read by
   *    screen readers.
   * @param {expression=} md-separator-keys An array of key codes used to separate chips.
   * @param {string=} md-chip-append-delay The number of milliseconds that the component will select
   *    a newly appended chip before allowing a user to type into the input. This is **necessary**
   *    for keyboard accessibility for screen readers. It defaults to 300ms and any number less than
   *    300 can cause issues with screen readers (particularly JAWS and sometimes NVDA).
   *
   *    _Available since Version 1.1.2._
   *
   *    **Note:** You can safely set this to `0` in one of the following two instances:
   *
   *    1. You are targeting an iOS or Safari-only application (where users would use VoiceOver) or
   *    only ChromeVox users.
   *
   *    2. If you have utilized the `md-separator-keys` to disable the `enter` keystroke in
   *    favor of another one (such as `,` or `;`).
   *
   * @usage
   * <hljs lang="html">
   *   <md-chips
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       readonly="isReadOnly">
   *   </md-chips>
   * </hljs>
   *
   * <h3>Validation</h3>
   * When using [ngMessages](https://docs.angularjs.org/api/ngMessages), you can show errors based
   * on our custom validators.
   * <hljs lang="html">
   *   <form name="userForm">
   *     <md-chips
   *       name="fruits"
   *       ng-model="myItems"
   *       placeholder="Add an item"
   *       md-max-chips="5">
   *     </md-chips>
   *     <div ng-messages="userForm.fruits.$error" ng-if="userForm.$dirty">
   *       <div ng-message="md-max-chips">You reached the maximum amount of chips</div>
   *    </div>
   *   </form>
   * </hljs>
   *
   */

  var MD_CHIPS_TEMPLATE = '\
      <md-chips-wrap\
          id="{{$mdChipsCtrl.wrapperId}}"\
          tabindex="{{$mdChipsCtrl.readonly ? 0 : -1}}"\
          ng-keydown="$mdChipsCtrl.chipKeydown($event)"\
          ng-class="{ \'md-focused\': $mdChipsCtrl.hasFocus(), \
                      \'md-readonly\': !$mdChipsCtrl.ngModelCtrl || $mdChipsCtrl.readonly,\
                      \'md-removable\': $mdChipsCtrl.isRemovable() }"\
          aria-setsize="{{$mdChipsCtrl.items.length}}"\
          class="md-chips">\
        <span ng-if="$mdChipsCtrl.readonly" class="md-visually-hidden">\
          {{$mdChipsCtrl.containerHint}}\
        </span>\
        <md-chip ng-repeat="$chip in $mdChipsCtrl.items"\
            index="{{$index}}"\
            ng-class="{\'md-focused\': $mdChipsCtrl.selectedChip == $index, \'md-readonly\': !$mdChipsCtrl.ngModelCtrl || $mdChipsCtrl.readonly}">\
          <div class="md-chip-content"\
              tabindex="{{$mdChipsCtrl.ariaTabIndex == $index ? 0 : -1}}"\
              id="{{$mdChipsCtrl.contentIdFor($index)}}"\
              role="option"\
              aria-selected="{{$mdChipsCtrl.selectedChip == $index}}" \
              aria-posinset="{{$index}}"\
              ng-click="!$mdChipsCtrl.readonly && $mdChipsCtrl.focusChip($index)"\
              ng-focus="!$mdChipsCtrl.readonly && $mdChipsCtrl.selectChip($index)"\
              md-chip-transclude="$mdChipsCtrl.chipContentsTemplate"></div>\
          <div ng-if="$mdChipsCtrl.isRemovable()"\
               class="md-chip-remove-container"\
               tabindex="-1"\
               md-chip-transclude="$mdChipsCtrl.chipRemoveTemplate"></div>\
        </md-chip>\
        <div class="md-chip-input-container" ng-if="!$mdChipsCtrl.readonly && $mdChipsCtrl.ngModelCtrl">\
          <div md-chip-transclude="$mdChipsCtrl.chipInputTemplate"></div>\
        </div>\
      </md-chips-wrap>';

  var CHIP_INPUT_TEMPLATE = '\
        <input\
            class="md-input"\
            tabindex="0"\
            aria-label="{{$mdChipsCtrl.inputAriaLabel}}" \
            placeholder="{{$mdChipsCtrl.getPlaceholder()}}"\
            ng-model="$mdChipsCtrl.chipBuffer"\
            ng-focus="$mdChipsCtrl.onInputFocus()"\
            ng-blur="$mdChipsCtrl.onInputBlur()"\
            ng-keydown="$mdChipsCtrl.inputKeydown($event)">';

  var CHIP_DEFAULT_TEMPLATE = '\
      <span>{{$chip}}</span>';

  var CHIP_REMOVE_TEMPLATE = '\
      <button\
          class="md-chip-remove"\
          ng-if="$mdChipsCtrl.isRemovable()"\
          ng-click="$mdChipsCtrl.removeChipAndFocusInput($$replacedScope.$index)"\
          type="button"\
          tabindex="-1">\
        <md-icon md-svg-src="{{ $mdChipsCtrl.mdCloseIcon }}"></md-icon>\
        <span class="md-visually-hidden">\
          {{$mdChipsCtrl.deleteButtonLabel}}\
        </span>\
      </button>';

  /**
   * MDChips Directive Definition
   */
  function MdChips ($mdTheming, $mdUtil, $compile, $log, $timeout, $$mdSvgRegistry) {
    // Run our templates through $mdUtil.processTemplate() to allow custom start/end symbols
    var templates = getTemplates();

    return {
      template: function(element, attrs) {
        // Clone the element into an attribute. By prepending the attribute
        // name with '$', AngularJS won't write it into the DOM. The cloned
        // element propagates to the link function via the attrs argument,
        // where various contained-elements can be consumed.
        attrs['$mdUserTemplate'] = element.clone();
        return templates.chips;
      },
      require: ['mdChips'],
      restrict: 'E',
      controller: 'MdChipsCtrl',
      controllerAs: '$mdChipsCtrl',
      bindToController: true,
      compile: compile,
      scope: {
        readonly: '=readonly',
        removable: '=mdRemovable',
        placeholder: '@',
        secondaryPlaceholder: '@',
        maxChips: '@mdMaxChips',
        transformChip: '&mdTransformChip',
        onAppend: '&mdOnAppend',
        onAdd: '&mdOnAdd',
        onRemove: '&mdOnRemove',
        onSelect: '&mdOnSelect',
        inputAriaLabel: '@',
        containerHint: '@',
        deleteHint: '@',
        deleteButtonLabel: '@',
        separatorKeys: '=?mdSeparatorKeys',
        requireMatch: '=?mdRequireMatch',
        chipAppendDelayString: '@?mdChipAppendDelay'
      }
    };

    /**
     * Builds the final template for `md-chips` and returns the postLink function.
     *
     * Building the template involves 3 key components:
     * static chips
     * chip template
     * input control
     *
     * If no `ng-model` is provided, only the static chip work needs to be done.
     *
     * If no user-passed `md-chip-template` exists, the default template is used. This resulting
     * template is appended to the chip content element.
     *
     * The remove button may be overridden by passing an element with an md-chip-remove attribute.
     *
     * If an `input` or `md-autocomplete` element is provided by the caller, it is set aside for
     * transclusion later. The transclusion happens in `postLink` as the parent scope is required.
     * If no user input is provided, a default one is appended to the input container node in the
     * template.
     *
     * Static Chips (i.e. `md-chip` elements passed from the caller) are gathered and set aside for
     * transclusion in the `postLink` function.
     *
     *
     * @param element
     * @param attr
     * @returns {Function}
     */
    function compile(element, attr) {
      // Grab the user template from attr and reset the attribute to null.
      var userTemplate = attr['$mdUserTemplate'];
      attr['$mdUserTemplate'] = null;

      var chipTemplate = getTemplateByQuery('md-chips>md-chip-template');

      var chipRemoveSelector = $mdUtil
        .prefixer()
        .buildList('md-chip-remove')
        .map(function(attr) {
          return 'md-chips>*[' + attr + ']';
        })
        .join(',');

      // Set the chip remove, chip contents and chip input templates. The link function will put
      // them on the scope for transclusion later.
      var chipRemoveTemplate   = getTemplateByQuery(chipRemoveSelector) || templates.remove,
          chipContentsTemplate = chipTemplate || templates.default,
          chipInputTemplate    = getTemplateByQuery('md-chips>md-autocomplete')
              || getTemplateByQuery('md-chips>input')
              || templates.input,
          staticChips = userTemplate.find('md-chip');

      // Warn of malformed template. See #2545
      if (userTemplate[0].querySelector('md-chip-template>*[md-chip-remove]')) {
        $log.warn('invalid placement of md-chip-remove within md-chip-template.');
      }

      function getTemplateByQuery (query) {
        if (!attr.ngModel) return;
        var element = userTemplate[0].querySelector(query);
        return element && element.outerHTML;
      }

      /**
       * Configures controller and transcludes.
       */
      return function postLink(scope, element, attrs, controllers) {
        $mdUtil.initOptionalProperties(scope, attr);

        $mdTheming(element);
        var mdChipsCtrl = controllers[0];
        if(chipTemplate) {
          // Chip editing functionality assumes we are using the default chip template.
          mdChipsCtrl.enableChipEdit = false;
        }

        mdChipsCtrl.chipContentsTemplate = chipContentsTemplate;
        mdChipsCtrl.chipRemoveTemplate   = chipRemoveTemplate;
        mdChipsCtrl.chipInputTemplate    = chipInputTemplate;

        mdChipsCtrl.mdCloseIcon = $$mdSvgRegistry.mdClose;

        element
            .attr({ tabindex: -1 })
            .on('focus', function () { mdChipsCtrl.onFocus(); });

        if (attr.ngModel) {
          mdChipsCtrl.configureNgModel(element.controller('ngModel'));

          // If an `md-transform-chip` attribute was set, tell the controller to use the expression
          // before appending chips.
          if (attrs.mdTransformChip) mdChipsCtrl.useTransformChipExpression();

          // If an `md-on-append` attribute was set, tell the controller to use the expression
          // when appending chips.
          //
          // DEPRECATED: Will remove in official 1.0 release
          if (attrs.mdOnAppend) mdChipsCtrl.useOnAppendExpression();

          // If an `md-on-add` attribute was set, tell the controller to use the expression
          // when adding chips.
          if (attrs.mdOnAdd) mdChipsCtrl.useOnAddExpression();

          // If an `md-on-remove` attribute was set, tell the controller to use the expression
          // when removing chips.
          if (attrs.mdOnRemove) mdChipsCtrl.useOnRemoveExpression();

          // If an `md-on-select` attribute was set, tell the controller to use the expression
          // when selecting chips.
          if (attrs.mdOnSelect) mdChipsCtrl.useOnSelectExpression();

          // The md-autocomplete and input elements won't be compiled until after this directive
          // is complete (due to their nested nature). Wait a tick before looking for them to
          // configure the controller.
          if (chipInputTemplate != templates.input) {
            // The autocomplete will not appear until the readonly attribute is not true (i.e.
            // false or undefined), so we have to watch the readonly and then on the next tick
            // after the chip transclusion has run, we can configure the autocomplete and user
            // input.
            scope.$watch('$mdChipsCtrl.readonly', function(readonly) {
              if (!readonly) {

                $mdUtil.nextTick(function(){

                  if (chipInputTemplate.indexOf('<md-autocomplete') === 0) {
                    var autocompleteEl = element.find('md-autocomplete');
                    mdChipsCtrl.configureAutocomplete(autocompleteEl.controller('mdAutocomplete'));
                  }

                  mdChipsCtrl.configureUserInput(element.find('input'));
                });
              }
            });
          }

          // At the next tick, if we find an input, make sure it has the md-input class
          $mdUtil.nextTick(function() {
            var input = element.find('input');

            input && input.toggleClass('md-input', true);
          });
        }

        // Compile with the parent's scope and prepend any static chips to the wrapper.
        if (staticChips.length > 0) {
          var compiledStaticChips = $compile(staticChips.clone())(scope.$parent);
          $timeout(function() { element.find('md-chips-wrap').prepend(compiledStaticChips); });
        }
      };
    }

    function getTemplates() {
      return {
        chips: $mdUtil.processTemplate(MD_CHIPS_TEMPLATE),
        input: $mdUtil.processTemplate(CHIP_INPUT_TEMPLATE),
        default: $mdUtil.processTemplate(CHIP_DEFAULT_TEMPLATE),
        remove: $mdUtil.processTemplate(CHIP_REMOVE_TEMPLATE)
      };
    }
  }

})();
(function(){
"use strict";

angular
    .module('material.components.chips')
    .controller('MdContactChipsCtrl', MdContactChipsCtrl);



/**
 * Controller for the MdContactChips component
 * @constructor
 */
function MdContactChipsCtrl () {
  /** @type {Object} */
  this.selectedItem = null;

  /** @type {string} */
  this.searchText = '';
}


MdContactChipsCtrl.prototype.queryContact = function(searchText) {
  return this.contactQuery({'$query': searchText});
};


MdContactChipsCtrl.prototype.itemName = function(item) {
  return item[this.contactName];
};

})();
(function(){
"use strict";


MdContactChips.$inject = ["$mdTheming", "$mdUtil"];angular
  .module('material.components.chips')
  .directive('mdContactChips', MdContactChips);

/**
 * @ngdoc directive
 * @name mdContactChips
 * @module material.components.chips
 *
 * @description
 * `<md-contact-chips>` is an input component based on `md-chips` and makes use of an
 * `md-autocomplete` element. The component allows the caller to supply a query expression which
 * returns  a list of possible contacts. The user can select one of these and add it to the list of
 * chips.
 *
 * You may also use the `md-highlight-text` directive along with its parameters to control the
 * appearance of the matched text inside of the contacts' autocomplete popup.
 *
 * @param {string=|object=} ng-model A model to bind the list of items to
 * @param {string=} placeholder Placeholder text that will be forwarded to the input.
 * @param {string=} secondary-placeholder Placeholder text that will be forwarded to the input,
 *    displayed when there is at least on item in the list
 * @param {expression} md-contacts An expression expected to return contacts matching the search
 *    test, `$query`. If this expression involves a promise, a loading bar is displayed while
 *    waiting for it to resolve.
 * @param {string} md-contact-name The field name of the contact object representing the
 *    contact's name.
 * @param {string} md-contact-email The field name of the contact object representing the
 *    contact's email address.
 * @param {string} md-contact-image The field name of the contact object representing the
 *    contact's image.
 * @param {number=} md-min-length Specifies the minimum length of text before autocomplete will
 *    make suggestions
 *
 * @param {expression=} filter-selected Whether to filter selected contacts from the list of
 *    suggestions shown in the autocomplete.
 *
 *    ***Note:** This attribute has been removed but may come back.*
 *
 *
 *
 * @usage
 * <hljs lang="html">
 *   <md-contact-chips
 *       ng-model="ctrl.contacts"
 *       md-contacts="ctrl.querySearch($query)"
 *       md-contact-name="name"
 *       md-contact-image="image"
 *       md-contact-email="email"
 *       placeholder="To">
 *   </md-contact-chips>
 * </hljs>
 *
 */


var MD_CONTACT_CHIPS_TEMPLATE = '\
      <md-chips class="md-contact-chips"\
          ng-model="$mdContactChipsCtrl.contacts"\
          md-require-match="$mdContactChipsCtrl.requireMatch"\
          md-chip-append-delay="{{$mdContactChipsCtrl.chipAppendDelay}}" \
          md-autocomplete-snap>\
          <md-autocomplete\
              md-menu-class="md-contact-chips-suggestions"\
              md-selected-item="$mdContactChipsCtrl.selectedItem"\
              md-search-text="$mdContactChipsCtrl.searchText"\
              md-items="item in $mdContactChipsCtrl.queryContact($mdContactChipsCtrl.searchText)"\
              md-item-text="$mdContactChipsCtrl.itemName(item)"\
              md-no-cache="true"\
              md-min-length="$mdContactChipsCtrl.minLength"\
              md-autoselect\
              placeholder="{{$mdContactChipsCtrl.contacts.length == 0 ?\
                  $mdContactChipsCtrl.placeholder : $mdContactChipsCtrl.secondaryPlaceholder}}">\
            <div class="md-contact-suggestion">\
              <img \
                  ng-src="{{item[$mdContactChipsCtrl.contactImage]}}"\
                  alt="{{item[$mdContactChipsCtrl.contactName]}}"\
                  ng-if="item[$mdContactChipsCtrl.contactImage]" />\
              <span class="md-contact-name" md-highlight-text="$mdContactChipsCtrl.searchText"\
                    md-highlight-flags="{{$mdContactChipsCtrl.highlightFlags}}">\
                {{item[$mdContactChipsCtrl.contactName]}}\
              </span>\
              <span class="md-contact-email" >{{item[$mdContactChipsCtrl.contactEmail]}}</span>\
            </div>\
          </md-autocomplete>\
          <md-chip-template>\
            <div class="md-contact-avatar">\
              <img \
                  ng-src="{{$chip[$mdContactChipsCtrl.contactImage]}}"\
                  alt="{{$chip[$mdContactChipsCtrl.contactName]}}"\
                  ng-if="$chip[$mdContactChipsCtrl.contactImage]" />\
            </div>\
            <div class="md-contact-name">\
              {{$chip[$mdContactChipsCtrl.contactName]}}\
            </div>\
          </md-chip-template>\
      </md-chips>';


/**
 * MDContactChips Directive Definition
 *
 * @param $mdTheming
 * @returns {*}
 * @ngInject
 */
function MdContactChips($mdTheming, $mdUtil) {
  return {
    template: function(element, attrs) {
      return MD_CONTACT_CHIPS_TEMPLATE;
    },
    restrict: 'E',
    controller: 'MdContactChipsCtrl',
    controllerAs: '$mdContactChipsCtrl',
    bindToController: true,
    compile: compile,
    scope: {
      contactQuery: '&mdContacts',
      placeholder: '@',
      secondaryPlaceholder: '@',
      contactName: '@mdContactName',
      contactImage: '@mdContactImage',
      contactEmail: '@mdContactEmail',
      contacts: '=ngModel',
      requireMatch: '=?mdRequireMatch',
      minLength: '=?mdMinLength',
      highlightFlags: '@?mdHighlightFlags',
      chipAppendDelay: '@?mdChipAppendDelay'
    }
  };

  function compile(element, attr) {
    return function postLink(scope, element, attrs, controllers) {
      var contactChipsController = controllers;

      $mdUtil.initOptionalProperties(scope, attr);
      $mdTheming(element);

      element.attr('tabindex', '-1');

      attrs.$observe('mdChipAppendDelay', function(newValue) {
        contactChipsController.chipAppendDelay = newValue;
      });
    };
  }
}

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc directive
   * @name mdCalendar
   * @module material.components.datepicker
   *
   * @param {Date} ng-model The component's model. Should be a Date object.
   * @param {Date=} md-min-date Expression representing the minimum date.
   * @param {Date=} md-max-date Expression representing the maximum date.
   * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a boolean whether it can be selected or not.
   *
   * @description
   * `<md-calendar>` is a component that renders a calendar that can be used to select a date.
   * It is a part of the `<md-datepicker>` pane, however it can also be used on it's own.
   *
   * @usage
   *
   * <hljs lang="html">
   *   <md-calendar ng-model="birthday"></md-calendar>
   * </hljs>
   */
  CalendarCtrl.$inject = ["$element", "$scope", "$$mdDateUtil", "$mdUtil", "$mdConstant", "$mdTheming", "$$rAF", "$attrs", "$mdDateLocale"];
  angular.module('material.components.datepicker')
    .directive('mdCalendar', calendarDirective);

  // POST RELEASE
  // TODO(jelbourn): Mac Cmd + left / right == Home / End
  // TODO(jelbourn): Refactor month element creation to use cloneNode (performance).
  // TODO(jelbourn): Define virtual scrolling constants (compactness) users can override.
  // TODO(jelbourn): Animated month transition on ng-model change (virtual-repeat)
  // TODO(jelbourn): Scroll snapping (virtual repeat)
  // TODO(jelbourn): Remove superfluous row from short months (virtual-repeat)
  // TODO(jelbourn): Month headers stick to top when scrolling.
  // TODO(jelbourn): Previous month opacity is lowered when partially scrolled out of view.
  // TODO(jelbourn): Support md-calendar standalone on a page (as a tabstop w/ aria-live
  //     announcement and key handling).
  // Read-only calendar (not just date-picker).

  function calendarDirective() {
    return {
      template: function(tElement, tAttr) {
        // TODO(crisbeto): This is a workaround that allows the calendar to work, without
        // a datepicker, until issue #8585 gets resolved. It can safely be removed
        // afterwards. This ensures that the virtual repeater scrolls to the proper place on load by
        // deferring the execution until the next digest. It's necessary only if the calendar is used
        // without a datepicker, otherwise it's already wrapped in an ngIf.
        var extraAttrs = tAttr.hasOwnProperty('ngIf') ? '' : 'ng-if="calendarCtrl.isInitialized"';
        var template = '' +
          '<div ng-switch="calendarCtrl.currentView" ' + extraAttrs + '>' +
            '<md-calendar-year ng-switch-when="year"></md-calendar-year>' +
            '<md-calendar-month ng-switch-default></md-calendar-month>' +
          '</div>';

        return template;
      },
      scope: {
        minDate: '=mdMinDate',
        maxDate: '=mdMaxDate',
        dateFilter: '=mdDateFilter',
        _currentView: '@mdCurrentView'
      },
      require: ['ngModel', 'mdCalendar'],
      controller: CalendarCtrl,
      controllerAs: 'calendarCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var ngModelCtrl = controllers[0];
        var mdCalendarCtrl = controllers[1];
        mdCalendarCtrl.configureNgModel(ngModelCtrl);
      }
    };
  }

  /**
   * Occasionally the hideVerticalScrollbar method might read an element's
   * width as 0, because it hasn't been laid out yet. This value will be used
   * as a fallback, in order to prevent scenarios where the element's width
   * would otherwise have been set to 0. This value is the "usual" width of a
   * calendar within a floating calendar pane.
   */
  var FALLBACK_WIDTH = 340;

  /** Next identifier for calendar instance. */
  var nextUniqueId = 0;

  /**
   * Controller for the mdCalendar component.
   * @ngInject @constructor
   */
  function CalendarCtrl($element, $scope, $$mdDateUtil, $mdUtil,
    $mdConstant, $mdTheming, $$rAF, $attrs, $mdDateLocale) {

    $mdTheming($element);

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.$mdUtil = $mdUtil;

    /** @final */
    this.keyCode = $mdConstant.KEY_CODE;

    /** @final */
    this.$$rAF = $$rAF;

    /** @final */
    this.$mdDateLocale = $mdDateLocale;

    /** @final {Date} */
    this.today = this.dateUtil.createDateAtMidnight();

    /** @type {!angular.NgModelController} */
    this.ngModelCtrl = null;

    /** @type {String} Class applied to the selected date cell. */
    this.SELECTED_DATE_CLASS = 'md-calendar-selected-date';

    /** @type {String} Class applied to the cell for today. */
    this.TODAY_CLASS = 'md-calendar-date-today';

    /** @type {String} Class applied to the focused cell. */
    this.FOCUSED_DATE_CLASS = 'md-focus';

    /** @final {number} Unique ID for this calendar instance. */
    this.id = nextUniqueId++;

    /**
     * The date that is currently focused or showing in the calendar. This will initially be set
     * to the ng-model value if set, otherwise to today. It will be updated as the user navigates
     * to other months. The cell corresponding to the displayDate does not necesarily always have
     * focus in the document (such as for cases when the user is scrolling the calendar).
     * @type {Date}
     */
    this.displayDate = null;

    /**
     * The selected date. Keep track of this separately from the ng-model value so that we
     * can know, when the ng-model value changes, what the previous value was before it's updated
     * in the component's UI.
     *
     * @type {Date}
     */
    this.selectedDate = null;

    /**
     * The first date that can be rendered by the calendar. The default is taken
     * from the mdDateLocale provider and is limited by the mdMinDate.
     * @type {Date}
     */
    this.firstRenderableDate = null;

    /**
     * The last date that can be rendered by the calendar. The default comes
     * from the mdDateLocale provider and is limited by the maxDate.
     * @type {Date}
     */
    this.lastRenderableDate = null;

    /**
     * Used to toggle initialize the root element in the next digest.
     * @type {Boolean}
     */
    this.isInitialized = false;

    /**
     * Cache for the  width of the element without a scrollbar. Used to hide the scrollbar later on
     * and to avoid extra reflows when switching between views.
     * @type {Number}
     */
    this.width = 0;

    /**
     * Caches the width of the scrollbar in order to be used when hiding it and to avoid extra reflows.
     * @type {Number}
     */
    this.scrollbarWidth = 0;

    // Unless the user specifies so, the calendar should not be a tab stop.
    // This is necessary because ngAria might add a tabindex to anything with an ng-model
    // (based on whether or not the user has turned that particular feature on/off).
    if (!$attrs.tabindex) {
      $element.attr('tabindex', '-1');
    }

    var boundKeyHandler = angular.bind(this, this.handleKeyEvent);



    // If use the md-calendar directly in the body without datepicker,
    // handleKeyEvent will disable other inputs on the page.
    // So only apply the handleKeyEvent on the body when the md-calendar inside datepicker,
    // otherwise apply on the calendar element only.

    var handleKeyElement;
    if ($element.parent().hasClass('md-datepicker-calendar')) {
      handleKeyElement = angular.element(document.body);
    } else {
      handleKeyElement = $element;
    }

    // Bind the keydown handler to the body, in order to handle cases where the focused
    // element gets removed from the DOM and stops propagating click events.
    handleKeyElement.on('keydown', boundKeyHandler);

    $scope.$on('$destroy', function() {
      handleKeyElement.off('keydown', boundKeyHandler);
    });

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
    // manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }

  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the $onInit hook.
   */
  CalendarCtrl.prototype.$onInit = function() {

    /**
     * The currently visible calendar view. Note the prefix on the scope value,
     * which is necessary, because the datepicker seems to reset the real one value if the
     * calendar is open, but the value on the datepicker's scope is empty.
     * @type {String}
     */
    this.currentView = this._currentView || 'month';

    var dateLocale = this.$mdDateLocale;

    if (this.minDate && this.minDate > dateLocale.firstRenderableDate) {
      this.firstRenderableDate = this.minDate;
    } else {
      this.firstRenderableDate = dateLocale.firstRenderableDate;
    }

    if (this.maxDate && this.maxDate < dateLocale.lastRenderableDate) {
      this.lastRenderableDate = this.maxDate;
    } else {
      this.lastRenderableDate = dateLocale.lastRenderableDate;
    }
  };

  /**
   * Sets up the controller's reference to ngModelController.
   * @param {!angular.NgModelController} ngModelCtrl
   */
  CalendarCtrl.prototype.configureNgModel = function(ngModelCtrl) {
    var self = this;

    self.ngModelCtrl = ngModelCtrl;

    self.$mdUtil.nextTick(function() {
      self.isInitialized = true;
    });

    ngModelCtrl.$render = function() {
      var value = this.$viewValue;

      // Notify the child scopes of any changes.
      self.$scope.$broadcast('md-calendar-parent-changed', value);

      // Set up the selectedDate if it hasn't been already.
      if (!self.selectedDate) {
        self.selectedDate = value;
      }

      // Also set up the displayDate.
      if (!self.displayDate) {
        self.displayDate = self.selectedDate || self.today;
      }
    };
  };

  /**
   * Sets the ng-model value for the calendar and emits a change event.
   * @param {Date} date
   */
  CalendarCtrl.prototype.setNgModelValue = function(date) {
    var value = this.dateUtil.createDateAtMidnight(date);
    this.focus(value);
    this.$scope.$emit('md-calendar-change', value);
    this.ngModelCtrl.$setViewValue(value);
    this.ngModelCtrl.$render();
    return value;
  };

  /**
   * Sets the current view that should be visible in the calendar
   * @param {string} newView View name to be set.
   * @param {number|Date} time Date object or a timestamp for the new display date.
   */
  CalendarCtrl.prototype.setCurrentView = function(newView, time) {
    var self = this;

    self.$mdUtil.nextTick(function() {
      self.currentView = newView;

      if (time) {
        self.displayDate = angular.isDate(time) ? time : new Date(time);
      }
    });
  };

  /**
   * Focus the cell corresponding to the given date.
   * @param {Date} date The date to be focused.
   */
  CalendarCtrl.prototype.focus = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var previousFocus = this.$element[0].querySelector('.md-focus');
      if (previousFocus) {
        previousFocus.classList.remove(this.FOCUSED_DATE_CLASS);
      }

      var cellId = this.getDateId(date, this.currentView);
      var cell = document.getElementById(cellId);
      if (cell) {
        cell.classList.add(this.FOCUSED_DATE_CLASS);
        cell.focus();
        this.displayDate = date;
      }
    } else {
      var rootElement = this.$element[0].querySelector('[ng-switch]');

      if (rootElement) {
        rootElement.focus();
      }
    }
  };

  /**
   * Normalizes the key event into an action name. The action will be broadcast
   * to the child controllers.
   * @param {KeyboardEvent} event
   * @returns {String} The action that should be taken, or null if the key
   * does not match a calendar shortcut.
   */
  CalendarCtrl.prototype.getActionFromKeyEvent = function(event) {
    var keyCode = this.keyCode;

    switch (event.which) {
      case keyCode.ENTER: return 'select';

      case keyCode.RIGHT_ARROW: return 'move-right';
      case keyCode.LEFT_ARROW: return 'move-left';

      // TODO(crisbeto): Might want to reconsider using metaKey, because it maps
      // to the "Windows" key on PC, which opens the start menu or resizes the browser.
      case keyCode.DOWN_ARROW: return event.metaKey ? 'move-page-down' : 'move-row-down';
      case keyCode.UP_ARROW: return event.metaKey ? 'move-page-up' : 'move-row-up';

      case keyCode.PAGE_DOWN: return 'move-page-down';
      case keyCode.PAGE_UP: return 'move-page-up';

      case keyCode.HOME: return 'start';
      case keyCode.END: return 'end';

      default: return null;
    }
  };

  /**
   * Handles a key event in the calendar with the appropriate action. The action will either
   * be to select the focused date or to navigate to focus a new date.
   * @param {KeyboardEvent} event
   */
  CalendarCtrl.prototype.handleKeyEvent = function(event) {
    var self = this;

    this.$scope.$apply(function() {
      // Capture escape and emit back up so that a wrapping component
      // (such as a date-picker) can decide to close.
      if (event.which == self.keyCode.ESCAPE || event.which == self.keyCode.TAB) {
        self.$scope.$emit('md-calendar-close');

        if (event.which == self.keyCode.TAB) {
          event.preventDefault();
        }

        return;
      }

      // Broadcast the action that any child controllers should take.
      var action = self.getActionFromKeyEvent(event);
      if (action) {
        event.preventDefault();
        event.stopPropagation();
        self.$scope.$broadcast('md-calendar-parent-action', action);
      }
    });
  };

  /**
   * Hides the vertical scrollbar on the calendar scroller of a child controller by
   * setting the width on the calendar scroller and the `overflow: hidden` wrapper
   * around the scroller, and then setting a padding-right on the scroller equal
   * to the width of the browser's scrollbar.
   *
   * This will cause a reflow.
   *
   * @param {object} childCtrl The child controller whose scrollbar should be hidden.
   */
  CalendarCtrl.prototype.hideVerticalScrollbar = function(childCtrl) {
    var self = this;
    var element = childCtrl.$element[0];
    var scrollMask = element.querySelector('.md-calendar-scroll-mask');

    if (self.width > 0) {
      setWidth();
    } else {
      self.$$rAF(function() {
        var scroller = childCtrl.calendarScroller;

        self.scrollbarWidth = scroller.offsetWidth - scroller.clientWidth;
        self.width = element.querySelector('table').offsetWidth;
        setWidth();
      });
    }

    function setWidth() {
      var width = self.width || FALLBACK_WIDTH;
      var scrollbarWidth = self.scrollbarWidth;
      var scroller = childCtrl.calendarScroller;

      scrollMask.style.width = width + 'px';
      scroller.style.width = (width + scrollbarWidth) + 'px';
      scroller.style.paddingRight = scrollbarWidth + 'px';
    }
  };

  /**
   * Gets an identifier for a date unique to the calendar instance for internal
   * purposes. Not to be displayed.
   * @param {Date} date The date for which the id is being generated
   * @param {string} namespace Namespace for the id. (month, year etc.)
   * @returns {string}
   */
  CalendarCtrl.prototype.getDateId = function(date, namespace) {
    if (!namespace) {
      throw new Error('A namespace for the date id has to be specified.');
    }

    return [
      'md',
      this.id,
      namespace,
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ].join('-');
  };

  /**
   * Util to trigger an extra digest on a parent scope, in order to to ensure that
   * any child virtual repeaters have updated. This is necessary, because the virtual
   * repeater doesn't update the $index the first time around since the content isn't
   * in place yet. The case, in which this is an issue, is when the repeater has less
   * than a page of content (e.g. a month or year view has a min or max date).
   */
  CalendarCtrl.prototype.updateVirtualRepeat = function() {
    var scope = this.$scope;
    var virtualRepeatResizeListener = scope.$on('$md-resize-enable', function() {
      if (!scope.$$phase) {
        scope.$apply();
      }

      virtualRepeatResizeListener();
    });
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarMonthCtrl.$inject = ["$element", "$scope", "$animate", "$q", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
    .directive('mdCalendarMonth', calendarDirective);

  /**
   * Height of one calendar month tbody. This must be made known to the virtual-repeat and is
   * subsequently used for scrolling to specific months.
   */
  var TBODY_HEIGHT = 265;

  /**
   * Height of a calendar month with a single row. This is needed to calculate the offset for
   * rendering an extra month in virtual-repeat that only contains one row.
   */
  var TBODY_SINGLE_ROW_HEIGHT = 45;

  /** Private directive that represents a list of months inside the calendar. */
  function calendarDirective() {
    return {
      template:
        '<table aria-hidden="true" class="md-calendar-day-header"><thead></thead></table>' +
        '<div class="md-calendar-scroll-mask">' +
        '<md-virtual-repeat-container class="md-calendar-scroll-container" ' +
              'md-offset-size="' + (TBODY_SINGLE_ROW_HEIGHT - TBODY_HEIGHT) + '">' +
            '<table role="grid" tabindex="0" class="md-calendar" aria-readonly="true">' +
              '<tbody ' +
                  'md-calendar-month-body ' +
                  'role="rowgroup" ' +
                  'md-virtual-repeat="i in monthCtrl.items" ' +
                  'md-month-offset="$index" ' +
                  'class="md-calendar-month" ' +
                  'md-start-index="monthCtrl.getSelectedMonthIndex()" ' +
                  'md-item-size="' + TBODY_HEIGHT + '">' +

                // The <tr> ensures that the <tbody> will always have the
                // proper height, even if it's empty. If it's content is
                // compiled, the <tr> will be overwritten.
                '<tr aria-hidden="true" md-force-height="\'' + TBODY_HEIGHT + 'px\'"></tr>' +
              '</tbody>' +
            '</table>' +
          '</md-virtual-repeat-container>' +
        '</div>',
      require: ['^^mdCalendar', 'mdCalendarMonth'],
      controller: CalendarMonthCtrl,
      controllerAs: 'monthCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var monthCtrl = controllers[1];
        monthCtrl.initialize(calendarCtrl);
      }
    };
  }

  /**
   * Controller for the calendar month component.
   * @ngInject @constructor
   */
  function CalendarMonthCtrl($element, $scope, $animate, $q,
    $$mdDateUtil, $mdDateLocale) {

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @final {!angular.$animate} */
    this.$animate = $animate;

    /** @final {!angular.$q} */
    this.$q = $q;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @final {HTMLElement} */
    this.calendarScroller = $element[0].querySelector('.md-virtual-repeat-scroller');

    /** @type {boolean} */
    this.isInitialized = false;

    /** @type {boolean} */
    this.isMonthTransitionInProgress = false;

    var self = this;

    /**
     * Handles a click event on a date cell.
     * Created here so that every cell can use the same function instance.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.cellClickHandler = function() {
      var timestamp = $$mdDateUtil.getTimestampFromNode(this);
      self.$scope.$apply(function() {
        self.calendarCtrl.setNgModelValue(timestamp);
      });
    };

    /**
     * Handles click events on the month headers. Switches
     * the calendar to the year view.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.headerClickHandler = function() {
      self.calendarCtrl.setCurrentView('year', $$mdDateUtil.getTimestampFromNode(this));
    };
  }

  /*** Initialization ***/

  /**
   * Initialize the controller by saving a reference to the calendar and
   * setting up the object that will be iterated by the virtual repeater.
   */
  CalendarMonthCtrl.prototype.initialize = function(calendarCtrl) {
    /**
     * Dummy array-like object for virtual-repeat to iterate over. The length is the total
     * number of months that can be viewed. We add 2 months: one to include the current month
     * and one for the last dummy month.
     *
     * This is shorter than ideal because of a (potential) Firefox bug
     * https://bugzilla.mozilla.org/show_bug.cgi?id=1181658.
     */

    this.items = {
      length: this.dateUtil.getMonthDistance(
        calendarCtrl.firstRenderableDate,
        calendarCtrl.lastRenderableDate
      ) + 2
    };

    this.calendarCtrl = calendarCtrl;
    this.attachScopeListeners();
    calendarCtrl.updateVirtualRepeat();

    // Fire the initial render, since we might have missed it the first time it fired.
    calendarCtrl.ngModelCtrl && calendarCtrl.ngModelCtrl.$render();
  };

  /**
   * Gets the "index" of the currently selected date as it would be in the virtual-repeat.
   * @returns {number}
   */
  CalendarMonthCtrl.prototype.getSelectedMonthIndex = function() {
    var calendarCtrl = this.calendarCtrl;

    return this.dateUtil.getMonthDistance(
      calendarCtrl.firstRenderableDate,
      calendarCtrl.displayDate || calendarCtrl.selectedDate || calendarCtrl.today
    );
  };

  /**
   * Change the selected date in the calendar (ngModel value has already been changed).
   * @param {Date} date
   */
  CalendarMonthCtrl.prototype.changeSelectedDate = function(date) {
    var self = this;
    var calendarCtrl = self.calendarCtrl;
    var previousSelectedDate = calendarCtrl.selectedDate;
    calendarCtrl.selectedDate = date;

    this.changeDisplayDate(date).then(function() {
      var selectedDateClass = calendarCtrl.SELECTED_DATE_CLASS;
      var namespace = 'month';

      // Remove the selected class from the previously selected date, if any.
      if (previousSelectedDate) {
        var prevDateCell = document.getElementById(calendarCtrl.getDateId(previousSelectedDate, namespace));
        if (prevDateCell) {
          prevDateCell.classList.remove(selectedDateClass);
          prevDateCell.setAttribute('aria-selected', 'false');
        }
      }

      // Apply the select class to the new selected date if it is set.
      if (date) {
        var dateCell = document.getElementById(calendarCtrl.getDateId(date, namespace));
        if (dateCell) {
          dateCell.classList.add(selectedDateClass);
          dateCell.setAttribute('aria-selected', 'true');
        }
      }
    });
  };

  /**
   * Change the date that is being shown in the calendar. If the given date is in a different
   * month, the displayed month will be transitioned.
   * @param {Date} date
   */
  CalendarMonthCtrl.prototype.changeDisplayDate = function(date) {
    // Initialization is deferred until this function is called because we want to reflect
    // the starting value of ngModel.
    if (!this.isInitialized) {
      this.buildWeekHeader();
      this.calendarCtrl.hideVerticalScrollbar(this);
      this.isInitialized = true;
      return this.$q.when();
    }

    // If trying to show an invalid date or a transition is in progress, do nothing.
    if (!this.dateUtil.isValidDate(date) || this.isMonthTransitionInProgress) {
      return this.$q.when();
    }

    this.isMonthTransitionInProgress = true;
    var animationPromise = this.animateDateChange(date);

    this.calendarCtrl.displayDate = date;

    var self = this;
    animationPromise.then(function() {
      self.isMonthTransitionInProgress = false;
    });

    return animationPromise;
  };

  /**
   * Animates the transition from the calendar's current month to the given month.
   * @param {Date} date
   * @returns {angular.$q.Promise} The animation promise.
   */
  CalendarMonthCtrl.prototype.animateDateChange = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var monthDistance = this.dateUtil.getMonthDistance(this.calendarCtrl.firstRenderableDate, date);
      this.calendarScroller.scrollTop = monthDistance * TBODY_HEIGHT;
    }

    return this.$q.when();
  };

  /**
   * Builds and appends a day-of-the-week header to the calendar.
   * This should only need to be called once during initialization.
   */
  CalendarMonthCtrl.prototype.buildWeekHeader = function() {
    var firstDayOfWeek = this.dateLocale.firstDayOfWeek;
    var shortDays = this.dateLocale.shortDays;

    var row = document.createElement('tr');
    for (var i = 0; i < 7; i++) {
      var th = document.createElement('th');
      th.textContent = shortDays[(i + firstDayOfWeek) % 7];
      row.appendChild(th);
    }

    this.$element.find('thead').append(row);
  };

  /**
   * Attaches listeners for the scope events that are broadcast by the calendar.
   */
  CalendarMonthCtrl.prototype.attachScopeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-parent-changed', function(event, value) {
      self.changeSelectedDate(value);
    });

    self.$scope.$on('md-calendar-parent-action', angular.bind(this, this.handleKeyEvent));
  };

  /**
   * Handles the month-specific keyboard interactions.
   * @param {Object} event Scope event object passed by the calendar.
   * @param {String} action Action, corresponding to the key that was pressed.
   */
  CalendarMonthCtrl.prototype.handleKeyEvent = function(event, action) {
    var calendarCtrl = this.calendarCtrl;
    var displayDate = calendarCtrl.displayDate;

    if (action === 'select') {
      calendarCtrl.setNgModelValue(displayDate);
    } else {
      var date = null;
      var dateUtil = this.dateUtil;

      switch (action) {
        case 'move-right': date = dateUtil.incrementDays(displayDate, 1); break;
        case 'move-left': date = dateUtil.incrementDays(displayDate, -1); break;

        case 'move-page-down': date = dateUtil.incrementMonths(displayDate, 1); break;
        case 'move-page-up': date = dateUtil.incrementMonths(displayDate, -1); break;

        case 'move-row-down': date = dateUtil.incrementDays(displayDate, 7); break;
        case 'move-row-up': date = dateUtil.incrementDays(displayDate, -7); break;

        case 'start': date = dateUtil.getFirstDateOfMonth(displayDate); break;
        case 'end': date = dateUtil.getLastDateOfMonth(displayDate); break;
      }

      if (date) {
        date = this.dateUtil.clampDate(date, calendarCtrl.minDate, calendarCtrl.maxDate);

        this.changeDisplayDate(date).then(function() {
          calendarCtrl.focus(date);
        });
      }
    }
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  mdCalendarMonthBodyDirective.$inject = ["$compile", "$$mdSvgRegistry"];
  CalendarMonthBodyCtrl.$inject = ["$element", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
      .directive('mdCalendarMonthBody', mdCalendarMonthBodyDirective);

  /**
   * Private directive consumed by md-calendar-month. Having this directive lets the calender use
   * md-virtual-repeat and also cleanly separates the month DOM construction functions from
   * the rest of the calendar controller logic.
   * @ngInject
   */
  function mdCalendarMonthBodyDirective($compile, $$mdSvgRegistry) {
    var ARROW_ICON = $compile('<md-icon md-svg-src="' +
      $$mdSvgRegistry.mdTabsArrow + '"></md-icon>')({})[0];

    return {
      require: ['^^mdCalendar', '^^mdCalendarMonth', 'mdCalendarMonthBody'],
      scope: { offset: '=mdMonthOffset' },
      controller: CalendarMonthBodyCtrl,
      controllerAs: 'mdMonthBodyCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var monthCtrl = controllers[1];
        var monthBodyCtrl = controllers[2];

        monthBodyCtrl.calendarCtrl = calendarCtrl;
        monthBodyCtrl.monthCtrl = monthCtrl;
        monthBodyCtrl.arrowIcon = ARROW_ICON.cloneNode(true);

        // The virtual-repeat re-uses the same DOM elements, so there are only a limited number
        // of repeated items that are linked, and then those elements have their bindings updated.
        // Since the months are not generated by bindings, we simply regenerate the entire thing
        // when the binding (offset) changes.
        scope.$watch(function() { return monthBodyCtrl.offset; }, function(offset) {
          if (angular.isNumber(offset)) {
            monthBodyCtrl.generateContent();
          }
        });
      }
    };
  }

  /**
   * Controller for a single calendar month.
   * @ngInject @constructor
   */
  function CalendarMonthBodyCtrl($element, $$mdDateUtil, $mdDateLocale) {
    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @type {Object} Reference to the month view. */
    this.monthCtrl = null;

    /** @type {Object} Reference to the calendar. */
    this.calendarCtrl = null;

    /**
     * Number of months from the start of the month "items" that the currently rendered month
     * occurs. Set via angular data binding.
     * @type {number}
     */
    this.offset = null;

    /**
     * Date cell to focus after appending the month to the document.
     * @type {HTMLElement}
     */
    this.focusAfterAppend = null;
  }

  /** Generate and append the content for this month to the directive element. */
  CalendarMonthBodyCtrl.prototype.generateContent = function() {
    var date = this.dateUtil.incrementMonths(this.calendarCtrl.firstRenderableDate, this.offset);

    this.$element
      .empty()
      .append(this.buildCalendarForMonth(date));

    if (this.focusAfterAppend) {
      this.focusAfterAppend.classList.add(this.calendarCtrl.FOCUSED_DATE_CLASS);
      this.focusAfterAppend.focus();
      this.focusAfterAppend = null;
    }
  };

  /**
   * Creates a single cell to contain a date in the calendar with all appropriate
   * attributes and classes added. If a date is given, the cell content will be set
   * based on the date.
   * @param {Date=} opt_date
   * @returns {HTMLElement}
   */
  CalendarMonthBodyCtrl.prototype.buildDateCell = function(opt_date) {
    var monthCtrl = this.monthCtrl;
    var calendarCtrl = this.calendarCtrl;

    // TODO(jelbourn): cloneNode is likely a faster way of doing this.
    var cell = document.createElement('td');
    cell.tabIndex = -1;
    cell.classList.add('md-calendar-date');
    cell.setAttribute('role', 'gridcell');

    if (opt_date) {
      cell.setAttribute('tabindex', '-1');
      cell.setAttribute('aria-label', this.dateLocale.longDateFormatter(opt_date));
      cell.id = calendarCtrl.getDateId(opt_date, 'month');

      // Use `data-timestamp` attribute because IE10 does not support the `dataset` property.
      cell.setAttribute('data-timestamp', opt_date.getTime());

      // TODO(jelourn): Doing these comparisons for class addition during generation might be slow.
      // It may be better to finish the construction and then query the node and add the class.
      if (this.dateUtil.isSameDay(opt_date, calendarCtrl.today)) {
        cell.classList.add(calendarCtrl.TODAY_CLASS);
      }

      if (this.dateUtil.isValidDate(calendarCtrl.selectedDate) &&
          this.dateUtil.isSameDay(opt_date, calendarCtrl.selectedDate)) {
        cell.classList.add(calendarCtrl.SELECTED_DATE_CLASS);
        cell.setAttribute('aria-selected', 'true');
      }

      var cellText = this.dateLocale.dates[opt_date.getDate()];

      if (this.isDateEnabled(opt_date)) {
        // Add a indicator for select, hover, and focus states.
        var selectionIndicator = document.createElement('span');
        selectionIndicator.classList.add('md-calendar-date-selection-indicator');
        selectionIndicator.textContent = cellText;
        cell.appendChild(selectionIndicator);
        cell.addEventListener('click', monthCtrl.cellClickHandler);

        if (calendarCtrl.displayDate && this.dateUtil.isSameDay(opt_date, calendarCtrl.displayDate)) {
          this.focusAfterAppend = cell;
        }
      } else {
        cell.classList.add('md-calendar-date-disabled');
        cell.textContent = cellText;
      }
    }

    return cell;
  };

  /**
   * Check whether date is in range and enabled
   * @param {Date=} opt_date
   * @return {boolean} Whether the date is enabled.
   */
  CalendarMonthBodyCtrl.prototype.isDateEnabled = function(opt_date) {
    return this.dateUtil.isDateWithinRange(opt_date,
          this.calendarCtrl.minDate, this.calendarCtrl.maxDate) &&
          (!angular.isFunction(this.calendarCtrl.dateFilter)
           || this.calendarCtrl.dateFilter(opt_date));
  };

  /**
   * Builds a `tr` element for the calendar grid.
   * @param rowNumber The week number within the month.
   * @returns {HTMLElement}
   */
  CalendarMonthBodyCtrl.prototype.buildDateRow = function(rowNumber) {
    var row = document.createElement('tr');
    row.setAttribute('role', 'row');

    // Because of an NVDA bug (with Firefox), the row needs an aria-label in order
    // to prevent the entire row being read aloud when the user moves between rows.
    // See http://community.nvda-project.org/ticket/4643.
    row.setAttribute('aria-label', this.dateLocale.weekNumberFormatter(rowNumber));

    return row;
  };

  /**
   * Builds the <tbody> content for the given date's month.
   * @param {Date=} opt_dateInMonth
   * @returns {DocumentFragment} A document fragment containing the <tr> elements.
   */
  CalendarMonthBodyCtrl.prototype.buildCalendarForMonth = function(opt_dateInMonth) {
    var date = this.dateUtil.isValidDate(opt_dateInMonth) ? opt_dateInMonth : new Date();

    var firstDayOfMonth = this.dateUtil.getFirstDateOfMonth(date);
    var firstDayOfTheWeek = this.getLocaleDay_(firstDayOfMonth);
    var numberOfDaysInMonth = this.dateUtil.getNumberOfDaysInMonth(date);

    // Store rows for the month in a document fragment so that we can append them all at once.
    var monthBody = document.createDocumentFragment();

    var rowNumber = 1;
    var row = this.buildDateRow(rowNumber);
    monthBody.appendChild(row);

    // If this is the final month in the list of items, only the first week should render,
    // so we should return immediately after the first row is complete and has been
    // attached to the body.
    var isFinalMonth = this.offset === this.monthCtrl.items.length - 1;

    // Add a label for the month. If the month starts on a Sun/Mon/Tues, the month label
    // goes on a row above the first of the month. Otherwise, the month label takes up the first
    // two cells of the first row.
    var blankCellOffset = 0;
    var monthLabelCell = document.createElement('td');
    var monthLabelCellContent = document.createElement('span');

    monthLabelCellContent.textContent = this.dateLocale.monthHeaderFormatter(date);
    monthLabelCell.appendChild(monthLabelCellContent);
    monthLabelCell.classList.add('md-calendar-month-label');
    // If the entire month is after the max date, render the label as a disabled state.
    if (this.calendarCtrl.maxDate && firstDayOfMonth > this.calendarCtrl.maxDate) {
      monthLabelCell.classList.add('md-calendar-month-label-disabled');
    } else {
      monthLabelCell.addEventListener('click', this.monthCtrl.headerClickHandler);
      monthLabelCell.setAttribute('data-timestamp', firstDayOfMonth.getTime());
      monthLabelCell.setAttribute('aria-label', this.dateLocale.monthFormatter(date));
      monthLabelCell.appendChild(this.arrowIcon.cloneNode(true));
    }

    if (firstDayOfTheWeek <= 2) {
      monthLabelCell.setAttribute('colspan', '7');

      var monthLabelRow = this.buildDateRow();
      monthLabelRow.appendChild(monthLabelCell);
      monthBody.insertBefore(monthLabelRow, row);

      if (isFinalMonth) {
        return monthBody;
      }
    } else {
      blankCellOffset = 3;
      monthLabelCell.setAttribute('colspan', '3');
      row.appendChild(monthLabelCell);
    }

    // Add a blank cell for each day of the week that occurs before the first of the month.
    // For example, if the first day of the month is a Tuesday, add blank cells for Sun and Mon.
    // The blankCellOffset is needed in cases where the first N cells are used by the month label.
    for (var i = blankCellOffset; i < firstDayOfTheWeek; i++) {
      row.appendChild(this.buildDateCell());
    }

    // Add a cell for each day of the month, keeping track of the day of the week so that
    // we know when to start a new row.
    var dayOfWeek = firstDayOfTheWeek;
    var iterationDate = firstDayOfMonth;
    for (var d = 1; d <= numberOfDaysInMonth; d++) {
      // If we've reached the end of the week, start a new row.
      if (dayOfWeek === 7) {
        // We've finished the first row, so we're done if this is the final month.
        if (isFinalMonth) {
          return monthBody;
        }
        dayOfWeek = 0;
        rowNumber++;
        row = this.buildDateRow(rowNumber);
        monthBody.appendChild(row);
      }

      iterationDate.setDate(d);
      var cell = this.buildDateCell(iterationDate);
      row.appendChild(cell);

      dayOfWeek++;
    }

    // Ensure that the last row of the month has 7 cells.
    while (row.childNodes.length < 7) {
      row.appendChild(this.buildDateCell());
    }

    // Ensure that all months have 6 rows. This is necessary for now because the virtual-repeat
    // requires that all items have exactly the same height.
    while (monthBody.childNodes.length < 6) {
      var whitespaceRow = this.buildDateRow();
      for (var j = 0; j < 7; j++) {
        whitespaceRow.appendChild(this.buildDateCell());
      }
      monthBody.appendChild(whitespaceRow);
    }

    return monthBody;
  };

  /**
   * Gets the day-of-the-week index for a date for the current locale.
   * @private
   * @param {Date} date
   * @returns {number} The column index of the date in the calendar.
   */
  CalendarMonthBodyCtrl.prototype.getLocaleDay_ = function(date) {
    return (date.getDay() + (7 - this.dateLocale.firstDayOfWeek)) % 7;
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarYearCtrl.$inject = ["$element", "$scope", "$animate", "$q", "$$mdDateUtil"];
  angular.module('material.components.datepicker')
    .directive('mdCalendarYear', calendarDirective);

  /**
   * Height of one calendar year tbody. This must be made known to the virtual-repeat and is
   * subsequently used for scrolling to specific years.
   */
  var TBODY_HEIGHT = 88;

  /** Private component, representing a list of years in the calendar. */
  function calendarDirective() {
    return {
      template:
        '<div class="md-calendar-scroll-mask">' +
          '<md-virtual-repeat-container class="md-calendar-scroll-container">' +
            '<table role="grid" tabindex="0" class="md-calendar" aria-readonly="true">' +
              '<tbody ' +
                  'md-calendar-year-body ' +
                  'role="rowgroup" ' +
                  'md-virtual-repeat="i in yearCtrl.items" ' +
                  'md-year-offset="$index" class="md-calendar-year" ' +
                  'md-start-index="yearCtrl.getFocusedYearIndex()" ' +
                  'md-item-size="' + TBODY_HEIGHT + '">' +
                // The <tr> ensures that the <tbody> will have the proper
                // height, even though it may be empty.
                '<tr aria-hidden="true" md-force-height="\'' + TBODY_HEIGHT + 'px\'"></tr>' +
              '</tbody>' +
            '</table>' +
          '</md-virtual-repeat-container>' +
        '</div>',
      require: ['^^mdCalendar', 'mdCalendarYear'],
      controller: CalendarYearCtrl,
      controllerAs: 'yearCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var yearCtrl = controllers[1];
        yearCtrl.initialize(calendarCtrl);
      }
    };
  }

  /**
   * Controller for the mdCalendar component.
   * @ngInject @constructor
   */
  function CalendarYearCtrl($element, $scope, $animate, $q, $$mdDateUtil) {

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @final {!angular.$animate} */
    this.$animate = $animate;

    /** @final {!angular.$q} */
    this.$q = $q;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final {HTMLElement} */
    this.calendarScroller = $element[0].querySelector('.md-virtual-repeat-scroller');

    /** @type {boolean} */
    this.isInitialized = false;

    /** @type {boolean} */
    this.isMonthTransitionInProgress = false;

    var self = this;

    /**
     * Handles a click event on a date cell.
     * Created here so that every cell can use the same function instance.
     * @this {HTMLTableCellElement} The cell that was clicked.
     */
    this.cellClickHandler = function() {
      self.calendarCtrl.setCurrentView('month', $$mdDateUtil.getTimestampFromNode(this));
    };
  }

  /**
   * Initialize the controller by saving a reference to the calendar and
   * setting up the object that will be iterated by the virtual repeater.
   */
  CalendarYearCtrl.prototype.initialize = function(calendarCtrl) {
    /**
     * Dummy array-like object for virtual-repeat to iterate over. The length is the total
     * number of years that can be viewed. We add 1 extra in order to include the current year.
     */
    this.items = {
      length: this.dateUtil.getYearDistance(
        calendarCtrl.firstRenderableDate,
        calendarCtrl.lastRenderableDate
      ) + 1
    };

    this.calendarCtrl = calendarCtrl;
    this.attachScopeListeners();
    calendarCtrl.updateVirtualRepeat();

    // Fire the initial render, since we might have missed it the first time it fired.
    calendarCtrl.ngModelCtrl && calendarCtrl.ngModelCtrl.$render();
  };

  /**
   * Gets the "index" of the currently selected date as it would be in the virtual-repeat.
   * @returns {number}
   */
  CalendarYearCtrl.prototype.getFocusedYearIndex = function() {
    var calendarCtrl = this.calendarCtrl;

    return this.dateUtil.getYearDistance(
      calendarCtrl.firstRenderableDate,
      calendarCtrl.displayDate || calendarCtrl.selectedDate || calendarCtrl.today
    );
  };

  /**
   * Change the date that is highlighted in the calendar.
   * @param {Date} date
   */
  CalendarYearCtrl.prototype.changeDate = function(date) {
    // Initialization is deferred until this function is called because we want to reflect
    // the starting value of ngModel.
    if (!this.isInitialized) {
      this.calendarCtrl.hideVerticalScrollbar(this);
      this.isInitialized = true;
      return this.$q.when();
    } else if (this.dateUtil.isValidDate(date) && !this.isMonthTransitionInProgress) {
      var self = this;
      var animationPromise = this.animateDateChange(date);

      self.isMonthTransitionInProgress = true;
      self.calendarCtrl.displayDate = date;

      return animationPromise.then(function() {
        self.isMonthTransitionInProgress = false;
      });
    }
  };

  /**
   * Animates the transition from the calendar's current month to the given month.
   * @param {Date} date
   * @returns {angular.$q.Promise} The animation promise.
   */
  CalendarYearCtrl.prototype.animateDateChange = function(date) {
    if (this.dateUtil.isValidDate(date)) {
      var monthDistance = this.dateUtil.getYearDistance(this.calendarCtrl.firstRenderableDate, date);
      this.calendarScroller.scrollTop = monthDistance * TBODY_HEIGHT;
    }

    return this.$q.when();
  };

  /**
   * Handles the year-view-specific keyboard interactions.
   * @param {Object} event Scope event object passed by the calendar.
   * @param {String} action Action, corresponding to the key that was pressed.
   */
  CalendarYearCtrl.prototype.handleKeyEvent = function(event, action) {
    var calendarCtrl = this.calendarCtrl;
    var displayDate = calendarCtrl.displayDate;

    if (action === 'select') {
      this.changeDate(displayDate).then(function() {
        calendarCtrl.setCurrentView('month', displayDate);
        calendarCtrl.focus(displayDate);
      });
    } else {
      var date = null;
      var dateUtil = this.dateUtil;

      switch (action) {
        case 'move-right': date = dateUtil.incrementMonths(displayDate, 1); break;
        case 'move-left': date = dateUtil.incrementMonths(displayDate, -1); break;

        case 'move-row-down': date = dateUtil.incrementMonths(displayDate, 6); break;
        case 'move-row-up': date = dateUtil.incrementMonths(displayDate, -6); break;
      }

      if (date) {
        var min = calendarCtrl.minDate ? dateUtil.getFirstDateOfMonth(calendarCtrl.minDate) : null;
        var max = calendarCtrl.maxDate ? dateUtil.getFirstDateOfMonth(calendarCtrl.maxDate) : null;
        date = dateUtil.getFirstDateOfMonth(this.dateUtil.clampDate(date, min, max));

        this.changeDate(date).then(function() {
          calendarCtrl.focus(date);
        });
      }
    }
  };

  /**
   * Attaches listeners for the scope events that are broadcast by the calendar.
   */
  CalendarYearCtrl.prototype.attachScopeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-parent-changed', function(event, value) {
      self.changeDate(value);
    });

    self.$scope.$on('md-calendar-parent-action', angular.bind(self, self.handleKeyEvent));
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  CalendarYearBodyCtrl.$inject = ["$element", "$$mdDateUtil", "$mdDateLocale"];
  angular.module('material.components.datepicker')
      .directive('mdCalendarYearBody', mdCalendarYearDirective);

  /**
   * Private component, consumed by the md-calendar-year, which separates the DOM construction logic
   * and allows for the year view to use md-virtual-repeat.
   */
  function mdCalendarYearDirective() {
    return {
      require: ['^^mdCalendar', '^^mdCalendarYear', 'mdCalendarYearBody'],
      scope: { offset: '=mdYearOffset' },
      controller: CalendarYearBodyCtrl,
      controllerAs: 'mdYearBodyCtrl',
      bindToController: true,
      link: function(scope, element, attrs, controllers) {
        var calendarCtrl = controllers[0];
        var yearCtrl = controllers[1];
        var yearBodyCtrl = controllers[2];

        yearBodyCtrl.calendarCtrl = calendarCtrl;
        yearBodyCtrl.yearCtrl = yearCtrl;

        scope.$watch(function() { return yearBodyCtrl.offset; }, function(offset) {
          if (angular.isNumber(offset)) {
            yearBodyCtrl.generateContent();
          }
        });
      }
    };
  }

  /**
   * Controller for a single year.
   * @ngInject @constructor
   */
  function CalendarYearBodyCtrl($element, $$mdDateUtil, $mdDateLocale) {
    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.dateLocale = $mdDateLocale;

    /** @type {Object} Reference to the calendar. */
    this.calendarCtrl = null;

    /** @type {Object} Reference to the year view. */
    this.yearCtrl = null;

    /**
     * Number of months from the start of the month "items" that the currently rendered month
     * occurs. Set via angular data binding.
     * @type {number}
     */
    this.offset = null;

    /**
     * Date cell to focus after appending the month to the document.
     * @type {HTMLElement}
     */
    this.focusAfterAppend = null;
  }

  /** Generate and append the content for this year to the directive element. */
  CalendarYearBodyCtrl.prototype.generateContent = function() {
    var date = this.dateUtil.incrementYears(this.calendarCtrl.firstRenderableDate, this.offset);

    this.$element
      .empty()
      .append(this.buildCalendarForYear(date));

    if (this.focusAfterAppend) {
      this.focusAfterAppend.classList.add(this.calendarCtrl.FOCUSED_DATE_CLASS);
      this.focusAfterAppend.focus();
      this.focusAfterAppend = null;
    }
  };

  /**
   * Creates a single cell to contain a year in the calendar.
   * @param {number} opt_year Four-digit year.
   * @param {number} opt_month Zero-indexed month.
   * @returns {HTMLElement}
   */
  CalendarYearBodyCtrl.prototype.buildMonthCell = function(year, month) {
    var calendarCtrl = this.calendarCtrl;
    var yearCtrl = this.yearCtrl;
    var cell = this.buildBlankCell();

    // Represent this month/year as a date.
    var firstOfMonth = new Date(year, month, 1);
    cell.setAttribute('aria-label', this.dateLocale.monthFormatter(firstOfMonth));
    cell.id = calendarCtrl.getDateId(firstOfMonth, 'year');

    // Use `data-timestamp` attribute because IE10 does not support the `dataset` property.
    cell.setAttribute('data-timestamp', firstOfMonth.getTime());

    if (this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.today)) {
      cell.classList.add(calendarCtrl.TODAY_CLASS);
    }

    if (this.dateUtil.isValidDate(calendarCtrl.selectedDate) &&
        this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.selectedDate)) {
      cell.classList.add(calendarCtrl.SELECTED_DATE_CLASS);
      cell.setAttribute('aria-selected', 'true');
    }

    var cellText = this.dateLocale.shortMonths[month];

    if (this.dateUtil.isMonthWithinRange(firstOfMonth,
        calendarCtrl.minDate, calendarCtrl.maxDate)) {
      var selectionIndicator = document.createElement('span');
      selectionIndicator.classList.add('md-calendar-date-selection-indicator');
      selectionIndicator.textContent = cellText;
      cell.appendChild(selectionIndicator);
      cell.addEventListener('click', yearCtrl.cellClickHandler);

      if (calendarCtrl.displayDate && this.dateUtil.isSameMonthAndYear(firstOfMonth, calendarCtrl.displayDate)) {
        this.focusAfterAppend = cell;
      }
    } else {
      cell.classList.add('md-calendar-date-disabled');
      cell.textContent = cellText;
    }

    return cell;
  };

  /**
   * Builds a blank cell.
   * @return {HTMLTableCellElement}
   */
  CalendarYearBodyCtrl.prototype.buildBlankCell = function() {
    var cell = document.createElement('td');
    cell.tabIndex = -1;
    cell.classList.add('md-calendar-date');
    cell.setAttribute('role', 'gridcell');

    cell.setAttribute('tabindex', '-1');
    return cell;
  };

  /**
   * Builds the <tbody> content for the given year.
   * @param {Date} date Date for which the content should be built.
   * @returns {DocumentFragment} A document fragment containing the months within the year.
   */
  CalendarYearBodyCtrl.prototype.buildCalendarForYear = function(date) {
    // Store rows for the month in a document fragment so that we can append them all at once.
    var year = date.getFullYear();
    var yearBody = document.createDocumentFragment();

    var monthCell, i;
    // First row contains label and Jan-Jun.
    var firstRow = document.createElement('tr');
    var labelCell = document.createElement('td');
    labelCell.className = 'md-calendar-month-label';
    labelCell.textContent = year;
    firstRow.appendChild(labelCell);

    for (i = 0; i < 6; i++) {
      firstRow.appendChild(this.buildMonthCell(year, i));
    }
    yearBody.appendChild(firstRow);

    // Second row contains a blank cell and Jul-Dec.
    var secondRow = document.createElement('tr');
    secondRow.appendChild(this.buildBlankCell());
    for (i = 6; i < 12; i++) {
      secondRow.appendChild(this.buildMonthCell(year, i));
    }
    yearBody.appendChild(secondRow);

    return yearBody;
  };
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * @ngdoc service
   * @name $mdDateLocaleProvider
   * @module material.components.datepicker
   *
   * @description
   * The `$mdDateLocaleProvider` is the provider that creates the `$mdDateLocale` service.
   * This provider that allows the user to specify messages, formatters, and parsers for date
   * internationalization. The `$mdDateLocale` service itself is consumed by AngularJS Material
   * components that deal with dates.
   *
   * @property {(Array<string>)=} months Array of month names (in order).
   * @property {(Array<string>)=} shortMonths Array of abbreviated month names.
   * @property {(Array<string>)=} days Array of the days of the week (in order).
   * @property {(Array<string>)=} shortDays Array of abbreviated days of the week.
   * @property {(Array<string>)=} dates Array of dates of the month. Only necessary for locales
   *     using a numeral system other than [1, 2, 3...].
   * @property {(Array<string>)=} firstDayOfWeek The first day of the week. Sunday = 0, Monday = 1,
   *    etc.
   * @property {(function(string): Date)=} parseDate Function to parse a date object from a string.
   * @property {(function(Date, string): string)=} formatDate Function to format a date object to a
   *     string. The datepicker directive also provides the time zone, if it was specified.
   * @property {(function(Date): string)=} monthHeaderFormatter Function that returns the label for
   *     a month given a date.
   * @property {(function(Date): string)=} monthFormatter Function that returns the full name of a month
   *     for a given date.
   * @property {(function(number): string)=} weekNumberFormatter Function that returns a label for
   *     a week given the week number.
   * @property {(string)=} msgCalendar Translation of the label "Calendar" for the current locale.
   * @property {(string)=} msgOpenCalendar Translation of the button label "Open calendar" for the
   *     current locale.
   * @property {Date=} firstRenderableDate The date from which the datepicker calendar will begin
   * rendering. Note that this will be ignored if a minimum date is set. Defaults to January 1st 1880.
   * @property {Date=} lastRenderableDate The last date that will be rendered by the datepicker
   * calendar. Note that this will be ignored if a maximum date is set. Defaults to January 1st 2130.
   *
   * @usage
   * <hljs lang="js">
   * myAppModule.config(function($mdDateLocaleProvider) {
   *
   *     // Example of a French localization.
   *     $mdDateLocaleProvider.months = ['janvier', 'février', 'mars', ...];
   *     $mdDateLocaleProvider.shortMonths = ['janv', 'févr', 'mars', ...];
   *     $mdDateLocaleProvider.days = ['dimanche', 'lundi', 'mardi', ...];
   *     $mdDateLocaleProvider.shortDays = ['Di', 'Lu', 'Ma', ...];
   *
   *     // Can change week display to start on Monday.
   *     $mdDateLocaleProvider.firstDayOfWeek = 1;
   *
   *     // Optional.
   *     $mdDateLocaleProvider.dates = [1, 2, 3, 4, 5, 6, ...];
   *
   *     // Example uses moment.js to parse and format dates.
   *     $mdDateLocaleProvider.parseDate = function(dateString) {
   *       var m = moment(dateString, 'L', true);
   *       return m.isValid() ? m.toDate() : new Date(NaN);
   *     };
   *
   *     $mdDateLocaleProvider.formatDate = function(date) {
   *       var m = moment(date);
   *       return m.isValid() ? m.format('L') : '';
   *     };
   *
   *     $mdDateLocaleProvider.monthHeaderFormatter = function(date) {
   *       return myShortMonths[date.getMonth()] + ' ' + date.getFullYear();
   *     };
   *
   *     // In addition to date display, date components also need localized messages
   *     // for aria-labels for screen-reader users.
   *
   *     $mdDateLocaleProvider.weekNumberFormatter = function(weekNumber) {
   *       return 'Semaine ' + weekNumber;
   *     };
   *
   *     $mdDateLocaleProvider.msgCalendar = 'Calendrier';
   *     $mdDateLocaleProvider.msgOpenCalendar = 'Ouvrir le calendrier';
   *
   *     // You can also set when your calendar begins and ends.
   *     $mdDateLocaleProvider.firstRenderableDate = new Date(1776, 6, 4);
   *     $mdDateLocaleProvider.lastRenderableDate = new Date(2012, 11, 21);
   * });
   * </hljs>
   *
   */
  angular.module('material.components.datepicker').config(["$provide", function($provide) {
    // TODO(jelbourn): Assert provided values are correctly formatted. Need assertions.

    /** @constructor */
    function DateLocaleProvider() {
      /** Array of full month names. E.g., ['January', 'Febuary', ...] */
      this.months = null;

      /** Array of abbreviated month names. E.g., ['Jan', 'Feb', ...] */
      this.shortMonths = null;

      /** Array of full day of the week names. E.g., ['Monday', 'Tuesday', ...] */
      this.days = null;

      /** Array of abbreviated dat of the week names. E.g., ['M', 'T', ...] */
      this.shortDays = null;

      /** Array of dates of a month (1 - 31). Characters might be different in some locales. */
      this.dates = null;

      /** Index of the first day of the week. 0 = Sunday, 1 = Monday, etc. */
      this.firstDayOfWeek = 0;

      /**
       * Function that converts the date portion of a Date to a string.
       * @type {(function(Date): string)}
       */
      this.formatDate = null;

      /**
       * Function that converts a date string to a Date object (the date portion)
       * @type {function(string): Date}
       */
      this.parseDate = null;

      /**
       * Function that formats a Date into a month header string.
       * @type {function(Date): string}
       */
      this.monthHeaderFormatter = null;

      /**
       * Function that formats a week number into a label for the week.
       * @type {function(number): string}
       */
      this.weekNumberFormatter = null;

      /**
       * Function that formats a date into a long aria-label that is read
       * when the focused date changes.
       * @type {function(Date): string}
       */
      this.longDateFormatter = null;

      /**
       * ARIA label for the calendar "dialog" used in the datepicker.
       * @type {string}
       */
      this.msgCalendar = '';

      /**
       * ARIA label for the datepicker's "Open calendar" buttons.
       * @type {string}
       */
      this.msgOpenCalendar = '';
    }

    /**
     * Factory function that returns an instance of the dateLocale service.
     * @ngInject
     * @param $locale
     * @returns {DateLocale}
     */
    DateLocaleProvider.prototype.$get = function($locale, $filter) {
      /**
       * Default date-to-string formatting function.
       * @param {!Date} date
       * @param {string=} timezone
       * @returns {string}
       */
      function defaultFormatDate(date, timezone) {
        if (!date) {
          return '';
        }

        // All of the dates created through ng-material *should* be set to midnight.
        // If we encounter a date where the localeTime shows at 11pm instead of midnight,
        // we have run into an issue with DST where we need to increment the hour by one:
        // var d = new Date(1992, 9, 8, 0, 0, 0);
        // d.toLocaleString(); // == "10/7/1992, 11:00:00 PM"
        var localeTime = date.toLocaleTimeString();
        var formatDate = date;
        if (date.getHours() === 0 &&
            (localeTime.indexOf('11:') !== -1 || localeTime.indexOf('23:') !== -1)) {
          formatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 1, 0, 0);
        }

        return $filter('date')(formatDate, 'M/d/yyyy', timezone);
      }

      /**
       * Default string-to-date parsing function.
       * @param {string} dateString
       * @returns {!Date}
       */
      function defaultParseDate(dateString) {
        return new Date(dateString);
      }

      /**
       * Default function to determine whether a string makes sense to be
       * parsed to a Date object.
       *
       * This is very permissive and is just a basic sanity check to ensure that
       * things like single integers aren't able to be parsed into dates.
       * @param {string} dateString
       * @returns {boolean}
       */
      function defaultIsDateComplete(dateString) {
        dateString = dateString.trim();

        // Looks for three chunks of content (either numbers or text) separated
        // by delimiters.
        var re = /^(([a-zA-Z]{3,}|[0-9]{1,4})([ \.,]+|[\/\-])){2}([a-zA-Z]{3,}|[0-9]{1,4})$/;
        return re.test(dateString);
      }

      /**
       * Default date-to-string formatter to get a month header.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultMonthHeaderFormatter(date) {
        return service.shortMonths[date.getMonth()] + ' ' + date.getFullYear();
      }

      /**
       * Default formatter for a month.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultMonthFormatter(date) {
        return service.months[date.getMonth()] + ' ' + date.getFullYear();
      }

      /**
       * Default week number formatter.
       * @param number
       * @returns {string}
       */
      function defaultWeekNumberFormatter(number) {
        return 'Week ' + number;
      }

      /**
       * Default formatter for date cell aria-labels.
       * @param {!Date} date
       * @returns {string}
       */
      function defaultLongDateFormatter(date) {
        // Example: 'Thursday June 18 2015'
        return [
          service.days[date.getDay()],
          service.months[date.getMonth()],
          service.dates[date.getDate()],
          date.getFullYear()
        ].join(' ');
      }

      // The default "short" day strings are the first character of each day,
      // e.g., "Monday" => "M".
      var defaultShortDays = $locale.DATETIME_FORMATS.SHORTDAY.map(function(day) {
        return day.substring(0, 1);
      });

      // The default dates are simply the numbers 1 through 31.
      var defaultDates = Array(32);
      for (var i = 1; i <= 31; i++) {
        defaultDates[i] = i;
      }

      // Default ARIA messages are in English (US).
      var defaultMsgCalendar = 'Calendar';
      var defaultMsgOpenCalendar = 'Open calendar';

      // Default start/end dates that are rendered in the calendar.
      var defaultFirstRenderableDate = new Date(1880, 0, 1);
      var defaultLastRendereableDate = new Date(defaultFirstRenderableDate.getFullYear() + 250, 0, 1);

      var service = {
        months: this.months || $locale.DATETIME_FORMATS.MONTH,
        shortMonths: this.shortMonths || $locale.DATETIME_FORMATS.SHORTMONTH,
        days: this.days || $locale.DATETIME_FORMATS.DAY,
        shortDays: this.shortDays || defaultShortDays,
        dates: this.dates || defaultDates,
        firstDayOfWeek: this.firstDayOfWeek || 0,
        formatDate: this.formatDate || defaultFormatDate,
        parseDate: this.parseDate || defaultParseDate,
        isDateComplete: this.isDateComplete || defaultIsDateComplete,
        monthHeaderFormatter: this.monthHeaderFormatter || defaultMonthHeaderFormatter,
        monthFormatter: this.monthFormatter || defaultMonthFormatter,
        weekNumberFormatter: this.weekNumberFormatter || defaultWeekNumberFormatter,
        longDateFormatter: this.longDateFormatter || defaultLongDateFormatter,
        msgCalendar: this.msgCalendar || defaultMsgCalendar,
        msgOpenCalendar: this.msgOpenCalendar || defaultMsgOpenCalendar,
        firstRenderableDate: this.firstRenderableDate || defaultFirstRenderableDate,
        lastRenderableDate: this.lastRenderableDate || defaultLastRendereableDate
      };

      return service;
    };
    DateLocaleProvider.prototype.$get.$inject = ["$locale", "$filter"];

    $provide.provider('$mdDateLocale', new DateLocaleProvider());
  }]);
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  /**
   * Utility for performing date calculations to facilitate operation of the calendar and
   * datepicker.
   */
  angular.module('material.components.datepicker').factory('$$mdDateUtil', function() {
    return {
      getFirstDateOfMonth: getFirstDateOfMonth,
      getNumberOfDaysInMonth: getNumberOfDaysInMonth,
      getDateInNextMonth: getDateInNextMonth,
      getDateInPreviousMonth: getDateInPreviousMonth,
      isInNextMonth: isInNextMonth,
      isInPreviousMonth: isInPreviousMonth,
      getDateMidpoint: getDateMidpoint,
      isSameMonthAndYear: isSameMonthAndYear,
      getWeekOfMonth: getWeekOfMonth,
      incrementDays: incrementDays,
      incrementMonths: incrementMonths,
      getLastDateOfMonth: getLastDateOfMonth,
      isSameDay: isSameDay,
      getMonthDistance: getMonthDistance,
      isValidDate: isValidDate,
      setDateTimeToMidnight: setDateTimeToMidnight,
      createDateAtMidnight: createDateAtMidnight,
      isDateWithinRange: isDateWithinRange,
      incrementYears: incrementYears,
      getYearDistance: getYearDistance,
      clampDate: clampDate,
      getTimestampFromNode: getTimestampFromNode,
      isMonthWithinRange: isMonthWithinRange
    };

    /**
     * Gets the first day of the month for the given date's month.
     * @param {Date} date
     * @returns {Date}
     */
    function getFirstDateOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    /**
     * Gets the number of days in the month for the given date's month.
     * @param date
     * @returns {number}
     */
    function getNumberOfDaysInMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    }

    /**
     * Get an arbitrary date in the month after the given date's month.
     * @param date
     * @returns {Date}
     */
    function getDateInNextMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 1);
    }

    /**
     * Get an arbitrary date in the month before the given date's month.
     * @param date
     * @returns {Date}
     */
    function getDateInPreviousMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() - 1, 1);
    }

    /**
     * Gets whether two dates have the same month and year.
     * @param {Date} d1
     * @param {Date} d2
     * @returns {boolean}
     */
    function isSameMonthAndYear(d1, d2) {
      return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
    }

    /**
     * Gets whether two dates are the same day (not not necesarily the same time).
     * @param {Date} d1
     * @param {Date} d2
     * @returns {boolean}
     */
    function isSameDay(d1, d2) {
      return d1.getDate() == d2.getDate() && isSameMonthAndYear(d1, d2);
    }

    /**
     * Gets whether a date is in the month immediately after some date.
     * @param {Date} startDate The date from which to compare.
     * @param {Date} endDate The date to check.
     * @returns {boolean}
     */
    function isInNextMonth(startDate, endDate) {
      var nextMonth = getDateInNextMonth(startDate);
      return isSameMonthAndYear(nextMonth, endDate);
    }

    /**
     * Gets whether a date is in the month immediately before some date.
     * @param {Date} startDate The date from which to compare.
     * @param {Date} endDate The date to check.
     * @returns {boolean}
     */
    function isInPreviousMonth(startDate, endDate) {
      var previousMonth = getDateInPreviousMonth(startDate);
      return isSameMonthAndYear(endDate, previousMonth);
    }

    /**
     * Gets the midpoint between two dates.
     * @param {Date} d1
     * @param {Date} d2
     * @returns {Date}
     */
    function getDateMidpoint(d1, d2) {
      return createDateAtMidnight((d1.getTime() + d2.getTime()) / 2);
    }

    /**
     * Gets the week of the month that a given date occurs in.
     * @param {Date} date
     * @returns {number} Index of the week of the month (zero-based).
     */
    function getWeekOfMonth(date) {
      var firstDayOfMonth = getFirstDateOfMonth(date);
      return Math.floor((firstDayOfMonth.getDay() + date.getDate() - 1) / 7);
    }

    /**
     * Gets a new date incremented by the given number of days. Number of days can be negative.
     * @param {Date} date
     * @param {number} numberOfDays
     * @returns {Date}
     */
    function incrementDays(date, numberOfDays) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() + numberOfDays);
    }

    /**
     * Gets a new date incremented by the given number of months. Number of months can be negative.
     * If the date of the given month does not match the target month, the date will be set to the
     * last day of the month.
     * @param {Date} date
     * @param {number} numberOfMonths
     * @returns {Date}
     */
    function incrementMonths(date, numberOfMonths) {
      // If the same date in the target month does not actually exist, the Date object will
      // automatically advance *another* month by the number of missing days.
      // For example, if you try to go from Jan. 30 to Feb. 30, you'll end up on March 2.
      // So, we check if the month overflowed and go to the last day of the target month instead.
      var dateInTargetMonth = new Date(date.getFullYear(), date.getMonth() + numberOfMonths, 1);
      var numberOfDaysInMonth = getNumberOfDaysInMonth(dateInTargetMonth);
      if (numberOfDaysInMonth < date.getDate()) {
        dateInTargetMonth.setDate(numberOfDaysInMonth);
      } else {
        dateInTargetMonth.setDate(date.getDate());
      }

      return dateInTargetMonth;
    }

    /**
     * Get the integer distance between two months. This *only* considers the month and year
     * portion of the Date instances.
     *
     * @param {Date} start
     * @param {Date} end
     * @returns {number} Number of months between `start` and `end`. If `end` is before `start`
     *     chronologically, this number will be negative.
     */
    function getMonthDistance(start, end) {
      return (12 * (end.getFullYear() - start.getFullYear())) + (end.getMonth() - start.getMonth());
    }

    /**
     * Gets the last day of the month for the given date.
     * @param {Date} date
     * @returns {Date}
     */
    function getLastDateOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), getNumberOfDaysInMonth(date));
    }

    /**
     * Checks whether a date is valid.
     * @param {Date} date
     * @return {boolean} Whether the date is a valid Date.
     */
    function isValidDate(date) {
      return date && date.getTime && !isNaN(date.getTime());
    }

    /**
     * Sets a date's time to midnight.
     * @param {Date} date
     */
    function setDateTimeToMidnight(date) {
      if (isValidDate(date)) {
        date.setHours(0, 0, 0, 0);
      }
    }

    /**
     * Creates a date with the time set to midnight.
     * Drop-in replacement for two forms of the Date constructor:
     * 1. No argument for Date representing now.
     * 2. Single-argument value representing number of seconds since Unix Epoch
     * or a Date object.
     * @param {number|Date=} opt_value
     * @return {Date} New date with time set to midnight.
     */
    function createDateAtMidnight(opt_value) {
      var date;
      if (angular.isUndefined(opt_value)) {
        date = new Date();
      } else {
        date = new Date(opt_value);
      }
      setDateTimeToMidnight(date);
      return date;
    }

     /**
      * Checks if a date is within a min and max range, ignoring the time component.
      * If minDate or maxDate are not dates, they are ignored.
      * @param {Date} date
      * @param {Date} minDate
      * @param {Date} maxDate
      */
     function isDateWithinRange(date, minDate, maxDate) {
       var dateAtMidnight = createDateAtMidnight(date);
       var minDateAtMidnight = isValidDate(minDate) ? createDateAtMidnight(minDate) : null;
       var maxDateAtMidnight = isValidDate(maxDate) ? createDateAtMidnight(maxDate) : null;
       return (!minDateAtMidnight || minDateAtMidnight <= dateAtMidnight) &&
           (!maxDateAtMidnight || maxDateAtMidnight >= dateAtMidnight);
     }

    /**
     * Gets a new date incremented by the given number of years. Number of years can be negative.
     * See `incrementMonths` for notes on overflow for specific dates.
     * @param {Date} date
     * @param {number} numberOfYears
     * @returns {Date}
     */
     function incrementYears(date, numberOfYears) {
       return incrementMonths(date, numberOfYears * 12);
     }

     /**
      * Get the integer distance between two years. This *only* considers the year portion of the
      * Date instances.
      *
      * @param {Date} start
      * @param {Date} end
      * @returns {number} Number of months between `start` and `end`. If `end` is before `start`
      *     chronologically, this number will be negative.
      */
     function getYearDistance(start, end) {
       return end.getFullYear() - start.getFullYear();
     }

     /**
      * Clamps a date between a minimum and a maximum date.
      * @param {Date} date Date to be clamped
      * @param {Date=} minDate Minimum date
      * @param {Date=} maxDate Maximum date
      * @return {Date}
      */
     function clampDate(date, minDate, maxDate) {
       var boundDate = date;
       if (minDate && date < minDate) {
         boundDate = new Date(minDate.getTime());
       }
       if (maxDate && date > maxDate) {
         boundDate = new Date(maxDate.getTime());
       }
       return boundDate;
     }

     /**
      * Extracts and parses the timestamp from a DOM node.
      * @param  {HTMLElement} node Node from which the timestamp will be extracted.
      * @return {number} Time since epoch.
      */
     function getTimestampFromNode(node) {
       if (node && node.hasAttribute('data-timestamp')) {
         return Number(node.getAttribute('data-timestamp'));
       }
     }

     /**
      * Checks if a month is within a min and max range, ignoring the date and time components.
      * If minDate or maxDate are not dates, they are ignored.
      * @param {Date} date
      * @param {Date} minDate
      * @param {Date} maxDate
      */
     function isMonthWithinRange(date, minDate, maxDate) {
       var month = date.getMonth();
       var year = date.getFullYear();

       return (!minDate || minDate.getFullYear() < year || minDate.getMonth() <= month) &&
        (!maxDate || maxDate.getFullYear() > year || maxDate.getMonth() >= month);
     }
  });
})();

})();
(function(){
"use strict";

(function() {
  'use strict';

  // POST RELEASE
  // TODO(jelbourn): Demo that uses moment.js
  // TODO(jelbourn): make sure this plays well with validation and ngMessages.
  // TODO(jelbourn): calendar pane doesn't open up outside of visible viewport.
  // TODO(jelbourn): forward more attributes to the internal input (required, autofocus, etc.)
  // TODO(jelbourn): something better for mobile (calendar panel takes up entire screen?)
  // TODO(jelbourn): input behavior (masking? auto-complete?)


  DatePickerCtrl.$inject = ["$scope", "$element", "$attrs", "$window", "$mdConstant", "$mdTheming", "$mdUtil", "$mdDateLocale", "$$mdDateUtil", "$$rAF", "$filter"];
  datePickerDirective.$inject = ["$$mdSvgRegistry", "$mdUtil", "$mdAria", "inputDirective"];
  angular.module('material.components.datepicker')
      .directive('mdDatepicker', datePickerDirective);

  /**
   * @ngdoc directive
   * @name mdDatepicker
   * @module material.components.datepicker
   *
   * @param {Date} ng-model The component's model. Expects either a JavaScript Date object or a value that can be parsed into one (e.g. a ISO 8601 string).
   * @param {Object=} ng-model-options Allows tuning of the way in which `ng-model` is being updated. Also allows
   * for a timezone to be specified. <a href="https://docs.angularjs.org/api/ng/directive/ngModelOptions#usage">Read more at the ngModelOptions docs.</a>
   * @param {expression=} ng-change Expression evaluated when the model value changes.
   * @param {expression=} ng-focus Expression evaluated when the input is focused or the calendar is opened.
   * @param {expression=} ng-blur Expression evaluated when focus is removed from the input or the calendar is closed.
   * @param {boolean=} ng-disabled Whether the datepicker is disabled.
   * @param {boolean=} ng-required Whether a value is required for the datepicker.
   * @param {Date=} md-min-date Expression representing a min date (inclusive).
   * @param {Date=} md-max-date Expression representing a max date (inclusive).
   * @param {(function(Date): boolean)=} md-date-filter Function expecting a date and returning a boolean whether it can be selected or not.
   * @param {String=} md-placeholder The date input placeholder value.
   * @param {String=} md-open-on-focus When present, the calendar will be opened when the input is focused.
   * @param {Boolean=} md-is-open Expression that can be used to open the datepicker's calendar on-demand.
   * @param {String=} md-current-view Default open view of the calendar pane. Can be either "month" or "year".
   * @param {String=} md-hide-icons Determines which datepicker icons should be hidden. Note that this may cause the
   * datepicker to not align properly with other components. **Use at your own risk.** Possible values are:
   * * `"all"` - Hides all icons.
   * * `"calendar"` - Only hides the calendar icon.
   * * `"triangle"` - Only hides the triangle icon.
   * @param {Object=} md-date-locale Allows for the values from the `$mdDateLocaleProvider` to be
   * ovewritten on a per-element basis (e.g. `msgOpenCalendar` can be overwritten with
   * `md-date-locale="{ msgOpenCalendar: 'Open a special calendar' }"`).
   *
   * @description
   * `<md-datepicker>` is a component used to select a single date.
   * For information on how to configure internationalization for the date picker,
   * see `$mdDateLocaleProvider`.
   *
   * This component supports [ngMessages](https://docs.angularjs.org/api/ngMessages/directive/ngMessages).
   * Supported attributes are:
   * * `required`: whether a required date is not set.
   * * `mindate`: whether the selected date is before the minimum allowed date.
   * * `maxdate`: whether the selected date is after the maximum allowed date.
   * * `debounceInterval`: ms to delay input processing (since last debounce reset); default value 500ms
   *
   * @usage
   * <hljs lang="html">
   *   <md-datepicker ng-model="birthday"></md-datepicker>
   * </hljs>
   *
   */

  function datePickerDirective($$mdSvgRegistry, $mdUtil, $mdAria, inputDirective) {
    return {
      template: function(tElement, tAttrs) {
        // Buttons are not in the tab order because users can open the calendar via keyboard
        // interaction on the text input, and multiple tab stops for one component (picker)
        // may be confusing.
        var hiddenIcons = tAttrs.mdHideIcons;
        var ariaLabelValue = tAttrs.ariaLabel || tAttrs.mdPlaceholder;

        var calendarButton = (hiddenIcons === 'all' || hiddenIcons === 'calendar') ? '' :
          '<md-button class="md-datepicker-button md-icon-button" type="button" ' +
              'tabindex="-1" aria-hidden="true" ' +
              'ng-click="ctrl.openCalendarPane($event)">' +
            '<md-icon class="md-datepicker-calendar-icon" aria-label="md-calendar" ' +
                     'md-svg-src="' + $$mdSvgRegistry.mdCalendar + '"></md-icon>' +
          '</md-button>';

        var triangleButton = '';

        if (hiddenIcons !== 'all' && hiddenIcons !== 'triangle') {
          triangleButton = '' +
            '<md-button type="button" md-no-ink ' +
              'class="md-datepicker-triangle-button md-icon-button" ' +
              'ng-click="ctrl.openCalendarPane($event)" ' +
              'aria-label="{{::ctrl.locale.msgOpenCalendar}}">' +
            '<div class="md-datepicker-expand-triangle"></div>' +
          '</md-button>';

          tElement.addClass(HAS_TRIANGLE_ICON_CLASS);
        }

        return calendarButton +
        '<div class="md-datepicker-input-container" ng-class="{\'md-datepicker-focused\': ctrl.isFocused}">' +
          '<input ' +
            (ariaLabelValue ? 'aria-label="' + ariaLabelValue + '" ' : '') +
            'class="md-datepicker-input" ' +
            'aria-haspopup="true" ' +
            'aria-expanded="{{ctrl.isCalendarOpen}}" ' +
            'ng-focus="ctrl.setFocused(true)" ' +
            'ng-blur="ctrl.setFocused(false)"> ' +
            triangleButton +
        '</div>' +

        // This pane will be detached from here and re-attached to the document body.
        '<div class="md-datepicker-calendar-pane md-whiteframe-z1" id="{{::ctrl.calendarPaneId}}">' +
          '<div class="md-datepicker-input-mask">' +
            '<div class="md-datepicker-input-mask-opaque"></div>' +
          '</div>' +
          '<div class="md-datepicker-calendar">' +
            '<md-calendar role="dialog" aria-label="{{::ctrl.locale.msgCalendar}}" ' +
                'md-current-view="{{::ctrl.currentView}}"' +
                'md-min-date="ctrl.minDate"' +
                'md-max-date="ctrl.maxDate"' +
                'md-date-filter="ctrl.dateFilter"' +
                'ng-model="ctrl.date" ng-if="ctrl.isCalendarOpen">' +
            '</md-calendar>' +
          '</div>' +
        '</div>';
      },
      require: ['ngModel', 'mdDatepicker', '?^mdInputContainer', '?^form'],
      scope: {
        minDate: '=mdMinDate',
        maxDate: '=mdMaxDate',
        placeholder: '@mdPlaceholder',
        currentView: '@mdCurrentView',
        dateFilter: '=mdDateFilter',
        isOpen: '=?mdIsOpen',
        debounceInterval: '=mdDebounceInterval',
        dateLocale: '=mdDateLocale'
      },
      controller: DatePickerCtrl,
      controllerAs: 'ctrl',
      bindToController: true,
      link: function(scope, element, attr, controllers) {
        var ngModelCtrl = controllers[0];
        var mdDatePickerCtrl = controllers[1];
        var mdInputContainer = controllers[2];
        var parentForm = controllers[3];
        var mdNoAsterisk = $mdUtil.parseAttributeBoolean(attr.mdNoAsterisk);

        mdDatePickerCtrl.configureNgModel(ngModelCtrl, mdInputContainer, inputDirective);

        if (mdInputContainer) {
          // We need to move the spacer after the datepicker itself,
          // because md-input-container adds it after the
          // md-datepicker-input by default. The spacer gets wrapped in a
          // div, because it floats and gets aligned next to the datepicker.
          // There are easier ways of working around this with CSS (making the
          // datepicker 100% wide, change the `display` etc.), however they
          // break the alignment with any other form controls.
          var spacer = element[0].querySelector('.md-errors-spacer');

          if (spacer) {
            element.after(angular.element('<div>').append(spacer));
          }

          mdInputContainer.setHasPlaceholder(attr.mdPlaceholder);
          mdInputContainer.input = element;
          mdInputContainer.element
            .addClass(INPUT_CONTAINER_CLASS)
            .toggleClass(HAS_CALENDAR_ICON_CLASS, attr.mdHideIcons !== 'calendar' && attr.mdHideIcons !== 'all');

          if (!mdInputContainer.label) {
            $mdAria.expect(element, 'aria-label', attr.mdPlaceholder);
          } else if(!mdNoAsterisk) {
            attr.$observe('required', function(value) {
              mdInputContainer.label.toggleClass('md-required', !!value);
            });
          }

          scope.$watch(mdInputContainer.isErrorGetter || function() {
            return ngModelCtrl.$invalid && (ngModelCtrl.$touched || (parentForm && parentForm.$submitted));
          }, mdInputContainer.setInvalid);
        } else if (parentForm) {
          // If invalid, highlights the input when the parent form is submitted.
          var parentSubmittedWatcher = scope.$watch(function() {
            return parentForm.$submitted;
          }, function(isSubmitted) {
            if (isSubmitted) {
              mdDatePickerCtrl.updateErrorState();
              parentSubmittedWatcher();
            }
          });
        }
      }
    };
  }

  /** Additional offset for the input's `size` attribute, which is updated based on its content. */
  var EXTRA_INPUT_SIZE = 3;

  /** Class applied to the container if the date is invalid. */
  var INVALID_CLASS = 'md-datepicker-invalid';

  /** Class applied to the datepicker when it's open. */
  var OPEN_CLASS = 'md-datepicker-open';

  /** Class applied to the md-input-container, if a datepicker is placed inside it */
  var INPUT_CONTAINER_CLASS = '_md-datepicker-floating-label';

  /** Class to be applied when the calendar icon is enabled. */
  var HAS_CALENDAR_ICON_CLASS = '_md-datepicker-has-calendar-icon';

  /** Class to be applied when the triangle icon is enabled. */
  var HAS_TRIANGLE_ICON_CLASS = '_md-datepicker-has-triangle-icon';

  /** Default time in ms to debounce input event by. */
  var DEFAULT_DEBOUNCE_INTERVAL = 500;

  /**
   * Height of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-height is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_HEIGHT = 368;

  /**
   * Width of the calendar pane used to check if the pane is going outside the boundary of
   * the viewport. See calendar.scss for how $md-calendar-width is computed; an extra 20px is
   * also added to space the pane away from the exact edge of the screen.
   *
   *  This is computed statically now, but can be changed to be measured if the circumstances
   *  of calendar sizing are changed.
   */
  var CALENDAR_PANE_WIDTH = 360;

  /** Used for checking whether the current user agent is on iOS or Android. */
  var IS_MOBILE_REGEX = /ipad|iphone|ipod|android/i;

  /**
   * Controller for md-datepicker.
   *
   * @ngInject @constructor
   */
  function DatePickerCtrl($scope, $element, $attrs, $window, $mdConstant,
    $mdTheming, $mdUtil, $mdDateLocale, $$mdDateUtil, $$rAF, $filter) {

    /** @final */
    this.$window = $window;

    /** @final */
    this.dateUtil = $$mdDateUtil;

    /** @final */
    this.$mdConstant = $mdConstant;

    /* @final */
    this.$mdUtil = $mdUtil;

    /** @final */
    this.$$rAF = $$rAF;

    /** @final */
    this.$mdDateLocale = $mdDateLocale;

    /**
     * The root document element. This is used for attaching a top-level click handler to
     * close the calendar panel when a click outside said panel occurs. We use `documentElement`
     * instead of body because, when scrolling is disabled, some browsers consider the body element
     * to be completely off the screen and propagate events directly to the html element.
     * @type {!angular.JQLite}
     */
    this.documentElement = angular.element(document.documentElement);

    /** @type {!angular.NgModelController} */
    this.ngModelCtrl = null;

    /** @type {HTMLInputElement} */
    this.inputElement = $element[0].querySelector('input');

    /** @final {!angular.JQLite} */
    this.ngInputElement = angular.element(this.inputElement);

    /** @type {HTMLElement} */
    this.inputContainer = $element[0].querySelector('.md-datepicker-input-container');

    /** @type {HTMLElement} Floating calendar pane. */
    this.calendarPane = $element[0].querySelector('.md-datepicker-calendar-pane');

    /** @type {HTMLElement} Calendar icon button. */
    this.calendarButton = $element[0].querySelector('.md-datepicker-button');

    /**
     * Element covering everything but the input in the top of the floating calendar pane.
     * @type {!angular.JQLite}
     */
    this.inputMask = angular.element($element[0].querySelector('.md-datepicker-input-mask-opaque'));

    /** @final {!angular.JQLite} */
    this.$element = $element;

    /** @final {!angular.Attributes} */
    this.$attrs = $attrs;

    /** @final {!angular.Scope} */
    this.$scope = $scope;

    /** @type {Date} */
    this.date = null;

    /** @type {boolean} */
    this.isFocused = false;

    /** @type {boolean} */
    this.isDisabled;
    this.setDisabled($element[0].disabled || angular.isString($attrs.disabled));

    /** @type {boolean} Whether the date-picker's calendar pane is open. */
    this.isCalendarOpen = false;

    /** @type {boolean} Whether the calendar should open when the input is focused. */
    this.openOnFocus = $attrs.hasOwnProperty('mdOpenOnFocus');

    /** @final */
    this.mdInputContainer = null;

    /**
     * Element from which the calendar pane was opened. Keep track of this so that we can return
     * focus to it when the pane is closed.
     * @type {HTMLElement}
     */
    this.calendarPaneOpenedFrom = null;

    /** @type {String} Unique id for the calendar pane. */
    this.calendarPaneId = 'md-date-pane-' + $mdUtil.nextUid();

    /** Pre-bound click handler is saved so that the event listener can be removed. */
    this.bodyClickHandler = angular.bind(this, this.handleBodyClick);

    /**
     * Name of the event that will trigger a close. Necessary to sniff the browser, because
     * the resize event doesn't make sense on mobile and can have a negative impact since it
     * triggers whenever the browser zooms in on a focused input.
     */
    this.windowEventName = IS_MOBILE_REGEX.test(
      navigator.userAgent || navigator.vendor || window.opera
    ) ? 'orientationchange' : 'resize';

    /** Pre-bound close handler so that the event listener can be removed. */
    this.windowEventHandler = $mdUtil.debounce(angular.bind(this, this.closeCalendarPane), 100);

    /** Pre-bound handler for the window blur event. Allows for it to be removed later. */
    this.windowBlurHandler = angular.bind(this, this.handleWindowBlur);

    /** The built-in AngularJS date filter. */
    this.ngDateFilter = $filter('date');

    /** @type {Number} Extra margin for the left side of the floating calendar pane. */
    this.leftMargin = 20;

    /** @type {Number} Extra margin for the top of the floating calendar. Gets determined on the first open. */
    this.topMargin = null;

    // Unless the user specifies so, the datepicker should not be a tab stop.
    // This is necessary because ngAria might add a tabindex to anything with an ng-model
    // (based on whether or not the user has turned that particular feature on/off).
    if ($attrs.tabindex) {
      this.ngInputElement.attr('tabindex', $attrs.tabindex);
      $attrs.$set('tabindex', null);
    } else {
      $attrs.$set('tabindex', '-1');
    }

    $attrs.$set('aria-owns', this.calendarPaneId);

    $mdTheming($element);
    $mdTheming(angular.element(this.calendarPane));

    var self = this;

    $scope.$on('$destroy', function() {
      self.detachCalendarPane();
    });

    if ($attrs.mdIsOpen) {
      $scope.$watch('ctrl.isOpen', function(shouldBeOpen) {
        if (shouldBeOpen) {
          self.openCalendarPane({
            target: self.inputElement
          });
        } else {
          self.closeCalendarPane();
        }
      });
    }

    // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
    // manually call the $onInit hook.
    if (angular.version.major === 1 && angular.version.minor <= 4) {
      this.$onInit();
    }

  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the $onInit hook.
   */
  DatePickerCtrl.prototype.$onInit = function() {

    /**
     * Holds locale-specific formatters, parsers, labels etc. Allows
     * the user to override specific ones from the $mdDateLocale provider.
     * @type {!Object}
     */
    this.locale = this.dateLocale ? angular.extend({}, this.$mdDateLocale, this.dateLocale) : this.$mdDateLocale;

    this.installPropertyInterceptors();
    this.attachChangeListeners();
    this.attachInteractionListeners();
  };

  /**
   * Sets up the controller's reference to ngModelController and
   * applies AngularJS's `input[type="date"]` directive.
   * @param {!angular.NgModelController} ngModelCtrl Instance of the ngModel controller.
   * @param {Object} mdInputContainer Instance of the mdInputContainer controller.
   * @param {Object} inputDirective Config for AngularJS's `input` directive.
   */
  DatePickerCtrl.prototype.configureNgModel = function(ngModelCtrl, mdInputContainer, inputDirective) {
    this.ngModelCtrl = ngModelCtrl;
    this.mdInputContainer = mdInputContainer;

    // The input needs to be [type="date"] in order to be picked up by AngularJS.
    this.$attrs.$set('type', 'date');

    // Invoke the `input` directive link function, adding a stub for the element.
    // This allows us to re-use AngularJS's logic for setting the timezone via ng-model-options.
    // It works by calling the link function directly which then adds the proper `$parsers` and
    // `$formatters` to the ngModel controller.
    inputDirective[0].link.pre(this.$scope, {
      on: angular.noop,
      val: angular.noop,
      0: {}
    }, this.$attrs, [ngModelCtrl]);

    var self = this;

    // Responds to external changes to the model value.
    self.ngModelCtrl.$formatters.push(function(value) {
      var parsedValue = angular.isDefined(value) ? Date.parse(value) : null;

      // `parsedValue` is the time since epoch if valid or `NaN` if invalid.
      if (!isNaN(parsedValue) && angular.isNumber(parsedValue)) {
        value = new Date(parsedValue);
      }

      if (value && !(value instanceof Date)) {
        throw Error(
          'The ng-model for md-datepicker must be a Date instance or a value ' +
          'that can be parsed into a date. Currently the model is of type: ' + typeof value
        );
      }

      self.onExternalChange(value);

      return value;
    });

    // Responds to external error state changes (e.g. ng-required based on another input).
    ngModelCtrl.$viewChangeListeners.unshift(angular.bind(this, this.updateErrorState));

    // Forwards any events from the input to the root element. This is necessary to get `updateOn`
    // working for events that don't bubble (e.g. 'blur') since AngularJS binds the handlers to
    // the `<md-datepicker>`.
    var updateOn = self.$mdUtil.getModelOption(ngModelCtrl, 'updateOn');

    if (updateOn) {
      this.ngInputElement.on(
        updateOn,
        angular.bind(this.$element, this.$element.triggerHandler, updateOn)
      );
    }
  };

  /**
   * Attach event listeners for both the text input and the md-calendar.
   * Events are used instead of ng-model so that updates don't infinitely update the other
   * on a change. This should also be more performant than using a $watch.
   */
  DatePickerCtrl.prototype.attachChangeListeners = function() {
    var self = this;

    self.$scope.$on('md-calendar-change', function(event, date) {
      self.setModelValue(date);
      self.onExternalChange(date);
      self.closeCalendarPane();
    });

    self.ngInputElement.on('input', angular.bind(self, self.resizeInputElement));

    var debounceInterval = angular.isDefined(this.debounceInterval) ?
        this.debounceInterval : DEFAULT_DEBOUNCE_INTERVAL;
    self.ngInputElement.on('input', self.$mdUtil.debounce(self.handleInputEvent,
        debounceInterval, self));
  };

  /** Attach event listeners for user interaction. */
  DatePickerCtrl.prototype.attachInteractionListeners = function() {
    var self = this;
    var $scope = this.$scope;
    var keyCodes = this.$mdConstant.KEY_CODE;

    // Add event listener through angular so that we can triggerHandler in unit tests.
    self.ngInputElement.on('keydown', function(event) {
      if (event.altKey && event.keyCode == keyCodes.DOWN_ARROW) {
        self.openCalendarPane(event);
        $scope.$digest();
      }
    });

    if (self.openOnFocus) {
      self.ngInputElement.on('focus', angular.bind(self, self.openCalendarPane));
      angular.element(self.$window).on('blur', self.windowBlurHandler);

      $scope.$on('$destroy', function() {
        angular.element(self.$window).off('blur', self.windowBlurHandler);
      });
    }

    $scope.$on('md-calendar-close', function() {
      self.closeCalendarPane();
    });
  };

  /**
   * Capture properties set to the date-picker and imperitively handle internal changes.
   * This is done to avoid setting up additional $watches.
   */
  DatePickerCtrl.prototype.installPropertyInterceptors = function() {
    var self = this;

    if (this.$attrs.ngDisabled) {
      // The expression is to be evaluated against the directive element's scope and not
      // the directive's isolate scope.
      var scope = this.$scope.$parent;

      if (scope) {
        scope.$watch(this.$attrs.ngDisabled, function(isDisabled) {
          self.setDisabled(isDisabled);
        });
      }
    }

    Object.defineProperty(this, 'placeholder', {
      get: function() { return self.inputElement.placeholder; },
      set: function(value) { self.inputElement.placeholder = value || ''; }
    });
  };

  /**
   * Sets whether the date-picker is disabled.
   * @param {boolean} isDisabled
   */
  DatePickerCtrl.prototype.setDisabled = function(isDisabled) {
    this.isDisabled = isDisabled;
    this.inputElement.disabled = isDisabled;

    if (this.calendarButton) {
      this.calendarButton.disabled = isDisabled;
    }
  };

  /**
   * Sets the custom ngModel.$error flags to be consumed by ngMessages. Flags are:
   *   - mindate: whether the selected date is before the minimum date.
   *   - maxdate: whether the selected flag is after the maximum date.
   *   - filtered: whether the selected date is allowed by the custom filtering function.
   *   - valid: whether the entered text input is a valid date
   *
   * The 'required' flag is handled automatically by ngModel.
   *
   * @param {Date=} opt_date Date to check. If not given, defaults to the datepicker's model value.
   */
  DatePickerCtrl.prototype.updateErrorState = function(opt_date) {
    var date = opt_date || this.date;

    // Clear any existing errors to get rid of anything that's no longer relevant.
    this.clearErrorState();

    if (this.dateUtil.isValidDate(date)) {
      // Force all dates to midnight in order to ignore the time portion.
      date = this.dateUtil.createDateAtMidnight(date);

      if (this.dateUtil.isValidDate(this.minDate)) {
        var minDate = this.dateUtil.createDateAtMidnight(this.minDate);
        this.ngModelCtrl.$setValidity('mindate', date >= minDate);
      }

      if (this.dateUtil.isValidDate(this.maxDate)) {
        var maxDate = this.dateUtil.createDateAtMidnight(this.maxDate);
        this.ngModelCtrl.$setValidity('maxdate', date <= maxDate);
      }

      if (angular.isFunction(this.dateFilter)) {
        this.ngModelCtrl.$setValidity('filtered', this.dateFilter(date));
      }
    } else {
      // The date is seen as "not a valid date" if there is *something* set
      // (i.e.., not null or undefined), but that something isn't a valid date.
      this.ngModelCtrl.$setValidity('valid', date == null);
    }

    angular.element(this.inputContainer).toggleClass(INVALID_CLASS, !this.ngModelCtrl.$valid);
  };

  /** Clears any error flags set by `updateErrorState`. */
  DatePickerCtrl.prototype.clearErrorState = function() {
    this.inputContainer.classList.remove(INVALID_CLASS);
    ['mindate', 'maxdate', 'filtered', 'valid'].forEach(function(field) {
      this.ngModelCtrl.$setValidity(field, true);
    }, this);
  };

  /** Resizes the input element based on the size of its content. */
  DatePickerCtrl.prototype.resizeInputElement = function() {
    this.inputElement.size = this.inputElement.value.length + EXTRA_INPUT_SIZE;
  };

  /**
   * Sets the model value if the user input is a valid date.
   * Adds an invalid class to the input element if not.
   */
  DatePickerCtrl.prototype.handleInputEvent = function() {
    var inputString = this.inputElement.value;
    var parsedDate = inputString ? this.locale.parseDate(inputString) : null;
    this.dateUtil.setDateTimeToMidnight(parsedDate);

    // An input string is valid if it is either empty (representing no date)
    // or if it parses to a valid date that the user is allowed to select.
    var isValidInput = inputString == '' || (
      this.dateUtil.isValidDate(parsedDate) &&
      this.locale.isDateComplete(inputString) &&
      this.isDateEnabled(parsedDate)
    );

    // The datepicker's model is only updated when there is a valid input.
    if (isValidInput) {
      this.setModelValue(parsedDate);
      this.date = parsedDate;
    }

    this.updateErrorState(parsedDate);
  };

  /**
   * Check whether date is in range and enabled
   * @param {Date=} opt_date
   * @return {boolean} Whether the date is enabled.
   */
  DatePickerCtrl.prototype.isDateEnabled = function(opt_date) {
    return this.dateUtil.isDateWithinRange(opt_date, this.minDate, this.maxDate) &&
          (!angular.isFunction(this.dateFilter) || this.dateFilter(opt_date));
  };

  /** Position and attach the floating calendar to the document. */
  DatePickerCtrl.prototype.attachCalendarPane = function() {
    var calendarPane = this.calendarPane;
    var body = document.body;

    calendarPane.style.transform = '';
    this.$element.addClass(OPEN_CLASS);
    this.mdInputContainer && this.mdInputContainer.element.addClass(OPEN_CLASS);
    angular.element(body).addClass('md-datepicker-is-showing');

    var elementRect = this.inputContainer.getBoundingClientRect();
    var bodyRect = body.getBoundingClientRect();

    if (!this.topMargin || this.topMargin < 0) {
      this.topMargin = (this.inputMask.parent().prop('clientHeight') - this.ngInputElement.prop('clientHeight')) / 2;
    }

    // Check to see if the calendar pane would go off the screen. If so, adjust position
    // accordingly to keep it within the viewport.
    var paneTop = elementRect.top - bodyRect.top - this.topMargin;
    var paneLeft = elementRect.left - bodyRect.left - this.leftMargin;

    // If ng-material has disabled body scrolling (for example, if a dialog is open),
    // then it's possible that the already-scrolled body has a negative top/left. In this case,
    // we want to treat the "real" top as (0 - bodyRect.top). In a normal scrolling situation,
    // though, the top of the viewport should just be the body's scroll position.
    var viewportTop = (bodyRect.top < 0 && document.body.scrollTop == 0) ?
        -bodyRect.top :
        document.body.scrollTop;

    var viewportLeft = (bodyRect.left < 0 && document.body.scrollLeft == 0) ?
        -bodyRect.left :
        document.body.scrollLeft;

    var viewportBottom = viewportTop + this.$window.innerHeight;
    var viewportRight = viewportLeft + this.$window.innerWidth;

    // Creates an overlay with a hole the same size as element. We remove a pixel or two
    // on each end to make it overlap slightly. The overlay's background is added in
    // the theme in the form of a box-shadow with a huge spread.
    this.inputMask.css({
      position: 'absolute',
      left: this.leftMargin + 'px',
      top: this.topMargin + 'px',
      width: (elementRect.width - 1) + 'px',
      height: (elementRect.height - 2) + 'px'
    });

    // If the right edge of the pane would be off the screen and shifting it left by the
    // difference would not go past the left edge of the screen. If the calendar pane is too
    // big to fit on the screen at all, move it to the left of the screen and scale the entire
    // element down to fit.
    if (paneLeft + CALENDAR_PANE_WIDTH > viewportRight) {
      if (viewportRight - CALENDAR_PANE_WIDTH > 0) {
        paneLeft = viewportRight - CALENDAR_PANE_WIDTH;
      } else {
        paneLeft = viewportLeft;
        var scale = this.$window.innerWidth / CALENDAR_PANE_WIDTH;
        calendarPane.style.transform = 'scale(' + scale + ')';
      }

      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    // If the bottom edge of the pane would be off the screen and shifting it up by the
    // difference would not go past the top edge of the screen.
    if (paneTop + CALENDAR_PANE_HEIGHT > viewportBottom &&
        viewportBottom - CALENDAR_PANE_HEIGHT > viewportTop) {
      paneTop = viewportBottom - CALENDAR_PANE_HEIGHT;
      calendarPane.classList.add('md-datepicker-pos-adjusted');
    }

    calendarPane.style.left = paneLeft + 'px';
    calendarPane.style.top = paneTop + 'px';
    document.body.appendChild(calendarPane);

    // Add CSS class after one frame to trigger open animation.
    this.$$rAF(function() {
      calendarPane.classList.add('md-pane-open');
    });
  };

  /** Detach the floating calendar pane from the document. */
  DatePickerCtrl.prototype.detachCalendarPane = function() {
    this.$element.removeClass(OPEN_CLASS);
    this.mdInputContainer && this.mdInputContainer.element.removeClass(OPEN_CLASS);
    angular.element(document.body).removeClass('md-datepicker-is-showing');
    this.calendarPane.classList.remove('md-pane-open');
    this.calendarPane.classList.remove('md-datepicker-pos-adjusted');

    if (this.isCalendarOpen) {
      this.$mdUtil.enableScrolling();
    }

    if (this.calendarPane.parentNode) {
      // Use native DOM removal because we do not want any of the
      // angular state of this element to be disposed.
      this.calendarPane.parentNode.removeChild(this.calendarPane);
    }
  };

  /**
   * Open the floating calendar pane.
   * @param {Event} event
   */
  DatePickerCtrl.prototype.openCalendarPane = function(event) {
    if (!this.isCalendarOpen && !this.isDisabled && !this.inputFocusedOnWindowBlur) {
      this.isCalendarOpen = this.isOpen = true;
      this.calendarPaneOpenedFrom = event.target;

      // Because the calendar pane is attached directly to the body, it is possible that the
      // rest of the component (input, etc) is in a different scrolling container, such as
      // an md-content. This means that, if the container is scrolled, the pane would remain
      // stationary. To remedy this, we disable scrolling while the calendar pane is open, which
      // also matches the native behavior for things like `<select>` on Mac and Windows.
      this.$mdUtil.disableScrollAround(this.calendarPane);

      this.attachCalendarPane();
      this.focusCalendar();
      this.evalAttr('ngFocus');

      // Attach click listener inside of a timeout because, if this open call was triggered by a
      // click, we don't want it to be immediately propogated up to the body and handled.
      var self = this;
      this.$mdUtil.nextTick(function() {
        // Use 'touchstart` in addition to click in order to work on iOS Safari, where click
        // events aren't propogated under most circumstances.
        // See http://www.quirksmode.org/blog/archives/2014/02/mouse_event_bub.html
        self.documentElement.on('click touchstart', self.bodyClickHandler);
      }, false);

      window.addEventListener(this.windowEventName, this.windowEventHandler);
    }
  };

  /** Close the floating calendar pane. */
  DatePickerCtrl.prototype.closeCalendarPane = function() {
    if (this.isCalendarOpen) {
      var self = this;

      self.detachCalendarPane();
      self.ngModelCtrl.$setTouched();
      self.evalAttr('ngBlur');

      self.documentElement.off('click touchstart', self.bodyClickHandler);
      window.removeEventListener(self.windowEventName, self.windowEventHandler);

      self.calendarPaneOpenedFrom.focus();
      self.calendarPaneOpenedFrom = null;

      if (self.openOnFocus) {
        // Ensures that all focus events have fired before resetting
        // the calendar. Prevents the calendar from reopening immediately
        // in IE when md-open-on-focus is set. Also it needs to trigger
        // a digest, in order to prevent issues where the calendar wasn't
        // showing up on the next open.
        self.$mdUtil.nextTick(reset);
      } else {
        reset();
      }
    }

    function reset(){
      self.isCalendarOpen = self.isOpen = false;
    }
  };

  /** Gets the controller instance for the calendar in the floating pane. */
  DatePickerCtrl.prototype.getCalendarCtrl = function() {
    return angular.element(this.calendarPane.querySelector('md-calendar')).controller('mdCalendar');
  };

  /** Focus the calendar in the floating pane. */
  DatePickerCtrl.prototype.focusCalendar = function() {
    // Use a timeout in order to allow the calendar to be rendered, as it is gated behind an ng-if.
    var self = this;
    this.$mdUtil.nextTick(function() {
      self.getCalendarCtrl().focus();
    }, false);
  };

  /**
   * Sets whether the input is currently focused.
   * @param {boolean} isFocused
   */
  DatePickerCtrl.prototype.setFocused = function(isFocused) {
    if (!isFocused) {
      this.ngModelCtrl.$setTouched();
    }

    // The ng* expressions shouldn't be evaluated when mdOpenOnFocus is on,
    // because they also get called when the calendar is opened/closed.
    if (!this.openOnFocus) {
      this.evalAttr(isFocused ? 'ngFocus' : 'ngBlur');
    }

    this.isFocused = isFocused;
  };

  /**
   * Handles a click on the document body when the floating calendar pane is open.
   * Closes the floating calendar pane if the click is not inside of it.
   * @param {MouseEvent} event
   */
  DatePickerCtrl.prototype.handleBodyClick = function(event) {
    if (this.isCalendarOpen) {
      var isInCalendar = this.$mdUtil.getClosest(event.target, 'md-calendar');

      if (!isInCalendar) {
        this.closeCalendarPane();
      }

      this.$scope.$digest();
    }
  };

  /**
   * Handles the event when the user navigates away from the current tab. Keeps track of
   * whether the input was focused when the event happened, in order to prevent the calendar
   * from re-opening.
   */
  DatePickerCtrl.prototype.handleWindowBlur = function() {
    this.inputFocusedOnWindowBlur = document.activeElement === this.inputElement;
  };

  /**
   * Evaluates an attribute expression against the parent scope.
   * @param {String} attr Name of the attribute to be evaluated.
   */
  DatePickerCtrl.prototype.evalAttr = function(attr) {
    if (this.$attrs[attr]) {
      this.$scope.$parent.$eval(this.$attrs[attr]);
    }
  };

  /**
   * Sets the ng-model value by first converting the date object into a strng. Converting it
   * is necessary, in order to pass AngularJS's `input[type="date"]` validations. AngularJS turns
   * the value into a Date object afterwards, before setting it on the model.
   * @param {Date=} value Date to be set as the model value.
   */
  DatePickerCtrl.prototype.setModelValue = function(value) {
    var timezone = this.$mdUtil.getModelOption(this.ngModelCtrl, 'timezone');
    this.ngModelCtrl.$setViewValue(this.ngDateFilter(value, 'yyyy-MM-dd', timezone));
  };

  /**
   * Updates the datepicker when a model change occurred externally.
   * @param {Date=} value Value that was set to the model.
   */
  DatePickerCtrl.prototype.onExternalChange = function(value) {
    var timezone = this.$mdUtil.getModelOption(this.ngModelCtrl, 'timezone');

    this.date = value;
    this.inputElement.value = this.locale.formatDate(value, timezone);
    this.mdInputContainer && this.mdInputContainer.setHasValue(!!value);
    this.resizeInputElement();
    this.updateErrorState();
  };
})();

})();
(function(){
"use strict";

angular
  .module('material.components.icon')
  .directive('mdIcon', ['$mdIcon', '$mdTheming', '$mdAria', '$sce', mdIconDirective]);

/**
 * @ngdoc directive
 * @name mdIcon
 * @module material.components.icon
 *
 * @restrict E
 *
 * @description
 * The `md-icon` directive makes it easier to use vector-based icons in your app (as opposed to
 * raster-based icons types like PNG). The directive supports both icon fonts and SVG icons.
 *
 * Icons should be considered view-only elements that should not be used directly as buttons; instead nest a `<md-icon>`
 * inside a `md-button` to add hover and click features.
 *
 * ### Icon fonts
 * Icon fonts are a technique in which you use a font where the glyphs in the font are
 * your icons instead of text. Benefits include a straightforward way to bundle everything into a
 * single HTTP request, simple scaling, easy color changing, and more.
 *
 * `md-icon` lets you consume an icon font by letting you reference specific icons in that font
 * by name rather than character code.
 *
 * ### SVG
 * For SVGs, the problem with using `<img>` or a CSS `background-image` is that you can't take
 * advantage of some SVG features, such as styling specific parts of the icon with CSS or SVG
 * animation.
 *
 * `md-icon` makes it easier to use SVG icons by *inlining* the SVG into an `<svg>` element in the
 * document. The most straightforward way of referencing an SVG icon is via URL, just like a
 * traditional `<img>`. `$mdIconProvider`, as a convenience, lets you _name_ an icon so you can
 * reference it by name instead of URL throughout your templates.
 *
 * Additionally, you may not want to make separate HTTP requests for every icon, so you can bundle
 * your SVG icons together and pre-load them with $mdIconProvider as an icon set. An icon set can
 * also be given a name, which acts as a namespace for individual icons, so you can reference them
 * like `"social:cake"`.
 *
 * When using SVGs, both external SVGs (via URLs) or sets of SVGs [from icon sets] can be
 * easily loaded and used. When using font-icons, developers must follow three (3) simple steps:
 *
 * <ol>
 * <li>Load the font library. e.g.<br/>
 *    `<link href="https://fonts.googleapis.com/icon?family=Material+Icons"
 *    rel="stylesheet">`
 * </li>
 * <li>
 *   Use either (a) font-icon class names or (b) a fontset and a font ligature to render the font glyph by
 *   using its textual name _or_ numerical character reference. Note that `material-icons` is the default fontset when
 *   none is specified.
 * </li>
 * <li> Use any of the following templates: <br/>
 *   <ul>
 *     <li>`<md-icon md-font-icon="classname"></md-icon>`</li>
 *     <li>`<md-icon md-font-set="font library classname or alias">textual_name</md-icon>`</li>
 *     <li>`<md-icon> numerical_character_reference </md-icon>`</li>
 *     <li>`<md-icon ng_bind="'textual_name'"></md-icon>`</li>
 *     <li>`<md-icon ng-bind="scopeVariable"></md-icon>`</li>
 *   </ul>
 * </li>
 * </ol>
 *
 * Full details for these steps can be found:
 *
 * <ul>
 * <li>http://google.github.io/material-design-icons/</li>
 * <li>http://google.github.io/material-design-icons/#icon-font-for-the-web</li>
 * </ul>
 *
 * The Material Design icon style <code>.material-icons</code> and the icon font references are published in
 * Material Design Icons:
 *
 * <ul>
 * <li>https://design.google.com/icons/</li>
 * <li>https://design.google.com/icons/#ic_accessibility</li>
 * </ul>
 *
 * ### Localization
 *
 * Because an `md-icon` element's text content is not intended to translated, it is recommended to declare the text
 * content for an `md-icon` element in its start tag. Instead of using the HTML text content, consider using `ng-bind`
 * with a scope variable or literal string.
 *
 * Examples:
 *
 * <ul>
 *   <li>`<md-icon ng-bind="myIconVariable"></md-icon>`</li>
 *   <li>`<md-icon ng-bind="'menu'"></md-icon>`
 * </ul>
 *
 * <h2 id="material_design_icons">Material Design Icons</h2>
 * Using the Material Design Icon-Selector, developers can easily and quickly search for a Material Design font-icon and
 * determine its textual name and character reference code. Click on any icon to see the slide-up information
 * panel with details regarding a SVG download or information on the font-icon usage.
 *
 * <a href="https://www.google.com/design/icons/#ic_accessibility" target="_blank" style="border-bottom:none;">
 * <img src="https://cloud.githubusercontent.com/assets/210413/7902490/fe8dd14c-0780-11e5-98fb-c821cc6475e6.png"
 *      aria-label="Material Design Icon-Selector" style="max-width:75%;padding-left:10%">
 * </a>
 *
 * <span class="image_caption">
 *  Click on the image above to link to the
 *  <a href="https://design.google.com/icons/#ic_accessibility" target="_blank">Material Design Icon-Selector</a>.
 * </span>
 *
 * @param {string} md-font-icon String name of CSS icon associated with the font-face will be used
 * to render the icon. Requires the fonts and the named CSS styles to be preloaded.
 * @param {string} md-font-set CSS style name associated with the font library; which will be assigned as
 * the class for the font-icon ligature. This value may also be an alias that is used to lookup the classname;
 * internally use `$mdIconProvider.fontSet(<alias>)` to determine the style name.
 * @param {string} md-svg-src String URL (or expression) used to load, cache, and display an
 *     external SVG.
 * @param {string} md-svg-icon md-svg-icon String name used for lookup of the icon from the internal cache;
 *     interpolated strings or expressions may also be used. Specific set names can be used with
 *     the syntax `<set name>:<icon name>`.<br/><br/>
 * To use icon sets, developers are required to pre-register the sets using the `$mdIconProvider` service.
 * @param {string=} aria-label Labels icon for accessibility. If an empty string is provided, icon
 * will be hidden from accessibility layer with `aria-hidden="true"`. If there's no aria-label on the icon
 * nor a label on the parent element, a warning will be logged to the console.
 * @param {string=} alt Labels icon for accessibility. If an empty string is provided, icon
 * will be hidden from accessibility layer with `aria-hidden="true"`. If there's no alt on the icon
 * nor a label on the parent element, a warning will be logged to the console.
 *
 * @usage
 * When using SVGs:
 * <hljs lang="html">
 *
 *  <!-- Icon ID; may contain optional icon set prefix; icons must registered using $mdIconProvider -->
 *  <md-icon md-svg-icon="social:android"    aria-label="android " ></md-icon>
 *
 *  <!-- Icon urls; may be preloaded in templateCache -->
 *  <md-icon md-svg-src="/android.svg"       aria-label="android " ></md-icon>
 *  <md-icon md-svg-src="{{ getAndroid() }}" aria-label="android " ></md-icon>
 *
 * </hljs>
 *
 * Use the <code>$mdIconProvider</code> to configure your application with
 * svg iconsets.
 *
 * <hljs lang="js">
 *  angular.module('appSvgIconSets', ['ngMaterial'])
 *    .controller('DemoCtrl', function($scope) {})
 *    .config(function($mdIconProvider) {
 *      $mdIconProvider
 *         .iconSet('social', 'img/icons/sets/social-icons.svg', 24)
 *         .defaultIconSet('img/icons/sets/core-icons.svg', 24);
 *     });
 * </hljs>
 *
 *
 * When using Font Icons with classnames:
 * <hljs lang="html">
 *
 *  <md-icon md-font-icon="android" aria-label="android" ></md-icon>
 *  <md-icon class="icon_home"      aria-label="Home"    ></md-icon>
 *
 * </hljs>
 *
 * When using Material Font Icons with ligatures:
 * <hljs lang="html">
 *  <!--
 *  For Material Design Icons
 *  The class '.material-icons' is auto-added if a style has NOT been specified
 *  since `material-icons` is the default fontset. So your markup:
 *  -->
 *  <md-icon> face </md-icon>
 *  <!-- becomes this at runtime: -->
 *  <md-icon md-font-set="material-icons"> face </md-icon>
 *  <!-- If the fontset does not support ligature names, then we need to use the ligature unicode.-->
 *  <md-icon> &#xE87C; </md-icon>
 *  <!-- The class '.material-icons' must be manually added if other styles are also specified-->
 *  <md-icon class="material-icons md-light md-48"> face </md-icon>
 * </hljs>
 *
 * When using other Font-Icon libraries:
 *
 * <hljs lang="js">
 *  // Specify a font-icon style alias
 *  angular.config(function($mdIconProvider) {
 *    $mdIconProvider.fontSet('md', 'material-icons');
 *  });
 * </hljs>
 *
 * <hljs lang="html">
 *  <md-icon md-font-set="md">favorite</md-icon>
 * </hljs>
 *
 */
function mdIconDirective($mdIcon, $mdTheming, $mdAria, $sce) {

  return {
    restrict: 'E',
    link : postLink
  };


  /**
   * Directive postLink
   * Supports embedded SVGs, font-icons, & external SVGs
   */
  function postLink(scope, element, attr) {
    $mdTheming(element);
    var lastFontIcon = attr.mdFontIcon;
    var lastFontSet = $mdIcon.fontSet(attr.mdFontSet);

    prepareForFontIcon();

    attr.$observe('mdFontIcon', fontIconChanged);
    attr.$observe('mdFontSet', fontIconChanged);

    // Keep track of the content of the svg src so we can compare against it later to see if the
    // attribute is static (and thus safe).
    var originalSvgSrc = element[0].getAttribute(attr.$attr.mdSvgSrc);

    // If using a font-icon, then the textual name of the icon itself
    // provides the aria-label.

    var attrName = attr.$normalize(attr.$attr.mdSvgIcon || attr.$attr.mdSvgSrc || '');

    /* Provide a default accessibility role of img */
    if (!attr.role) {
      $mdAria.expect(element, 'role', 'img');
      /* manually update attr variable */
      attr.role = 'img';
    }

    /* Don't process ARIA if already valid */
    if ( attr.role === "img" && !attr.ariaHidden && !$mdAria.hasAriaLabel(element) ) {
      var iconName;
      if (attr.alt) {
        /* Use alt text by default if available */
        $mdAria.expect(element, 'aria-label', attr.alt);
      } else if ($mdAria.parentHasAriaLabel(element, 2)) {
        /* Parent has ARIA so we will assume it will describe the image */
        $mdAria.expect(element, 'aria-hidden', 'true');
      } else if (iconName = (attr.mdFontIcon || attr.mdSvgIcon || element.text())) {
        /* Use icon name as aria-label */
        $mdAria.expect(element, 'aria-label', iconName);
      } else {
        /* No label found */
        $mdAria.expect(element, 'aria-hidden', 'true');
      }
    }

    if (attrName) {
      // Use either pre-configured SVG or URL source, respectively.
      attr.$observe(attrName, function(attrVal) {
        element.empty();
        if (attrVal) {
          $mdIcon(attrVal)
            .then(function(svg) {
            element.empty();
            element.append(svg);
          });
        }
      });
    }

    function prepareForFontIcon() {
      if (!attr.mdSvgIcon && !attr.mdSvgSrc) {
        if (attr.mdFontIcon) {
          element.addClass('md-font ' + attr.mdFontIcon);
        }

        element.addClass(lastFontSet);
      }
    }

    function fontIconChanged() {
      if (!attr.mdSvgIcon && !attr.mdSvgSrc) {
        if (attr.mdFontIcon) {
          element.removeClass(lastFontIcon);
          element.addClass(attr.mdFontIcon);

          lastFontIcon = attr.mdFontIcon;
        }

        var fontSet = $mdIcon.fontSet(attr.mdFontSet);

        if (lastFontSet !== fontSet) {
          element.removeClass(lastFontSet);
          element.addClass(fontSet);

          lastFontSet = fontSet;
        }
      }
    }
  }
}

})();
(function(){
"use strict";

  
MdIconService.$inject = ["config", "$templateRequest", "$q", "$log", "$mdUtil", "$sce"];angular
    .module('material.components.icon')
    .constant('$$mdSvgRegistry', {
        'mdTabsArrow':   'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwb2x5Z29uIHBvaW50cz0iMTUuNCw3LjQgMTQsNiA4LDEyIDE0LDE4IDE1LjQsMTYuNiAxMC44LDEyICIvPjwvZz48L3N2Zz4=',
        'mdClose':       'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik0xOSA2LjQxbC0xLjQxLTEuNDEtNS41OSA1LjU5LTUuNTktNS41OS0xLjQxIDEuNDEgNS41OSA1LjU5LTUuNTkgNS41OSAxLjQxIDEuNDEgNS41OS01LjU5IDUuNTkgNS41OSAxLjQxLTEuNDEtNS41OS01LjU5eiIvPjwvZz48L3N2Zz4=',
        'mdCancel':      'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik0xMiAyYy01LjUzIDAtMTAgNC40Ny0xMCAxMHM0LjQ3IDEwIDEwIDEwIDEwLTQuNDcgMTAtMTAtNC40Ny0xMC0xMC0xMHptNSAxMy41OWwtMS40MSAxLjQxLTMuNTktMy41OS0zLjU5IDMuNTktMS40MS0xLjQxIDMuNTktMy41OS0zLjU5LTMuNTkgMS40MS0xLjQxIDMuNTkgMy41OSAzLjU5LTMuNTkgMS40MSAxLjQxLTMuNTkgMy41OSAzLjU5IDMuNTl6Ii8+PC9nPjwvc3ZnPg==',
        'mdMenu':        'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxwYXRoIGQ9Ik0zLDZIMjFWOEgzVjZNMywxMUgyMVYxM0gzVjExTTMsMTZIMjFWMThIM1YxNloiIC8+PC9zdmc+',
        'mdToggleArrow': 'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgNDggNDgiPjxwYXRoIGQ9Ik0yNCAxNmwtMTIgMTIgMi44MyAyLjgzIDkuMTctOS4xNyA5LjE3IDkuMTcgMi44My0yLjgzeiIvPjxwYXRoIGQ9Ik0wIDBoNDh2NDhoLTQ4eiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==',
        'mdCalendar':    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTkgM2gtMVYxaC0ydjJIOFYxSDZ2Mkg1Yy0xLjExIDAtMS45OS45LTEuOTkgMkwzIDE5YzAgMS4xLjg5IDIgMiAyaDE0YzEuMSAwIDItLjkgMi0yVjVjMC0xLjEtLjktMi0yLTJ6bTAgMTZINVY4aDE0djExek03IDEwaDV2NUg3eiIvPjwvc3ZnPg==',
        'mdChecked':     'data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiPjxnPjxwYXRoIGQ9Ik05IDE2LjE3TDQuODMgMTJsLTEuNDIgMS40MUw5IDE5IDIxIDdsLTEuNDEtMS40MXoiLz48L2c+PC9zdmc+'
    })
    .provider('$mdIcon', MdIconProvider);

/**
 * @ngdoc service
 * @name $mdIconProvider
 * @module material.components.icon
 *
 * @description
 * `$mdIconProvider` is used only to register icon IDs with URLs. These configuration features allow
 * icons and icon sets to be pre-registered and associated with source URLs **before** the `<md-icon />`
 * directives are compiled.
 *
 * If using font-icons, the developer is responsible for loading the fonts.
 *
 * If using SVGs, loading of the actual svg files are deferred to on-demand requests and are loaded
 * internally by the `$mdIcon` service using the `$templateRequest` service. When an SVG is
 * requested by name/ID, the `$mdIcon` service searches its registry for the associated source URL;
 * that URL is used to on-demand load and parse the SVG dynamically.
 *
 * The `$templateRequest` service expects the icons source to be loaded over trusted URLs.<br/>
 * This means, when loading icons from an external URL, you have to trust the URL in the `$sceDelegateProvider`.
 *
 * <hljs lang="js">
 *   app.config(function($sceDelegateProvider) {
 *     $sceDelegateProvider.resourceUrlWhitelist([
 *       // Adding 'self' to the whitelist, will allow requests from the current origin.
 *       'self',
 *       // Using double asterisks here, will allow all URLs to load.
 *       // We recommend to only specify the given domain you want to allow.
 *       '**'
 *     ]);
 *   });
 * </hljs>
 *
 * Read more about the [$sceDelegateProvider](https://docs.angularjs.org/api/ng/provider/$sceDelegateProvider).
 *
 * **Notice:** Most font-icons libraries do not support ligatures (for example `fontawesome`).<br/>
 *  In such cases you are not able to use the icon's ligature name - Like so:
 *
 *  <hljs lang="html">
 *    <md-icon md-font-set="fa">fa-bell</md-icon>
 *  </hljs>
 *
 * You should instead use the given unicode, instead of the ligature name.
 *
 * <p ng-hide="true"> ##// Notice we can't use a hljs element here, because the characters will be escaped.</p>
 *  ```html
 *    <md-icon md-font-set="fa">&#xf0f3</md-icon>
 *  ```
 *
 * All unicode ligatures are prefixed with the `&#x` string.
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultFontSet( 'fa' )                   // This sets our default fontset className.
    *          .defaultIconSet('my/app/icons.svg')       // Register a default set of SVG icons
    *          .iconSet('social', 'my/app/social.svg')   // Register a named icon set of SVGs
    *          .icon('android', 'my/app/android.svg')    // Register a specific icon (by name)
    *          .icon('work:chair', 'my/app/chair.svg');  // Register icon in a specific set
    *   });
 * </hljs>
 *
 * SVG icons and icon sets can be easily pre-loaded and cached using either (a) a build process or (b) a runtime
 * **startup** process (shown below):
 *
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Register a default set of SVG icon definitions
    *     $mdIconProvider.defaultIconSet('my/app/icons.svg')
    *
    *   })
 *   .run(function($templateRequest){
    *
    *     // Pre-fetch icons sources by URL and cache in the $templateCache...
    *     // subsequent $templateRequest calls will look there first.
    *
    *     var urls = [ 'imy/app/icons.svg', 'img/icons/android.svg'];
    *
    *     angular.forEach(urls, function(url) {
    *       $templateRequest(url);
    *     });
    *
    *   });
 *
 * </hljs>
 *
 * > <b>Note:</b> The loaded SVG data is subsequently cached internally for future requests.
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#icon
 *
 * @description
 * Register a source URL for a specific icon name; the name may include optional 'icon set' name prefix.
 * These icons  will later be retrieved from the cache using `$mdIcon( <icon name> )`
 *
 * @param {string} id Icon name/id used to register the icon
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height the icon's viewBox.
 * It is ignored for icons with an existing viewBox. Default size is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .icon('android', 'my/app/android.svg')    // Register a specific icon (by name)
    *          .icon('work:chair', 'my/app/chair.svg');  // Register icon in a specific set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#iconSet
 *
 * @description
 * Register a source URL for a 'named' set of icons; group of SVG definitions where each definition
 * has an icon id. Individual icons can be subsequently retrieved from this cached set using
 * `$mdIcon(<icon set name>:<icon name>)`
 *
 * @param {string} id Icon name/id used to register the iconset
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height of the viewBox of all icons in the set.
 * It is ignored for icons with an existing viewBox. All icons in the icon set should be the same size.
 * Default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .iconSet('social', 'my/app/social.svg')   // Register a named icon set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#defaultIconSet
 *
 * @description
 * Register a source URL for the default 'named' set of icons. Unless explicitly registered,
 * subsequent lookups of icons will failover to search this 'default' icon set.
 * Icon can be retrieved from this cached, default set using `$mdIcon(<name>)`
 *
 * @param {string} url specifies the external location for the data file. Used internally by
 * `$templateRequest` to load the data or as part of the lookup in `$templateCache` if pre-loading
 * was configured.
 * @param {number=} viewBoxSize Sets the width and height of the viewBox of all icons in the set.
 * It is ignored for icons with an existing viewBox. All icons in the icon set should be the same size.
 * Default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultIconSet( 'my/app/social.svg' )   // Register a default icon set
    *   });
 * </hljs>
 *
 */
/**
 * @ngdoc method
 * @name $mdIconProvider#defaultFontSet
 *
 * @description
 * When using Font-Icons, AngularJS Material assumes the the Material Design icons will be used and automatically
 * configures the default font-set == 'material-icons'. Note that the font-set references the font-icon library
 * class style that should be applied to the `<md-icon>`.
 *
 * Configuring the default means that the attributes
 * `md-font-set="material-icons"` or `class="material-icons"` do not need to be explicitly declared on the
 * `<md-icon>` markup. For example:
 *
 *  `<md-icon> face </md-icon>`
 *  will render as
 *  `<span class="material-icons"> face </span>`, and
 *
 *  `<md-icon md-font-set="fa"> face </md-icon>`
 *  will render as
 *  `<span class="fa"> face </span>`
 *
 * @param {string} name of the font-library style that should be applied to the md-icon DOM element
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
   *     $mdIconProvider.defaultFontSet( 'fa' );
   *   });
 * </hljs>
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#fontSet
 *
 * @description
 * When using a font set for `<md-icon>` you must specify the correct font classname in the `md-font-set`
 * attribute. If the fonset className is really long, your markup may become cluttered... an easy
 * solution is to define an `alias` for your fontset:
 *
 * @param {string} alias of the specified fontset.
 * @param {string} className of the fontset.
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
   *     // In this case, we set an alias for the `material-icons` fontset.
   *     $mdIconProvider.fontSet('md', 'material-icons');
   *   });
 * </hljs>
 *
 */

/**
 * @ngdoc method
 * @name $mdIconProvider#defaultViewBoxSize
 *
 * @description
 * While `<md-icon />` markup can also be style with sizing CSS, this method configures
 * the default width **and** height used for all icons; unless overridden by specific CSS.
 * The default sizing is (24px, 24px).
 * @param {number=} viewBoxSize Sets the width and height of the viewBox for an icon or an icon set.
 * All icons in a set should be the same size. The default value is 24.
 *
 * @returns {obj} an `$mdIconProvider` reference; used to support method call chains for the API
 *
 * @usage
 * <hljs lang="js">
 *   app.config(function($mdIconProvider) {
    *
    *     // Configure URLs for icons specified by [set:]id.
    *
    *     $mdIconProvider
    *          .defaultViewBoxSize(36)   // Register a default icon size (width == height)
    *   });
 * </hljs>
 *
 */

var config = {
  defaultViewBoxSize: 24,
  defaultFontSet: 'material-icons',
  fontSets: []
};

function MdIconProvider() {
}

MdIconProvider.prototype = {
  icon: function(id, url, viewBoxSize) {
    if (id.indexOf(':') == -1) id = '$default:' + id;

    config[id] = new ConfigurationItem(url, viewBoxSize);
    return this;
  },

  iconSet: function(id, url, viewBoxSize) {
    config[id] = new ConfigurationItem(url, viewBoxSize);
    return this;
  },

  defaultIconSet: function(url, viewBoxSize) {
    var setName = '$default';

    if (!config[setName]) {
      config[setName] = new ConfigurationItem(url, viewBoxSize);
    }

    config[setName].viewBoxSize = viewBoxSize || config.defaultViewBoxSize;

    return this;
  },

  defaultViewBoxSize: function(viewBoxSize) {
    config.defaultViewBoxSize = viewBoxSize;
    return this;
  },

  /**
   * Register an alias name associated with a font-icon library style ;
   */
  fontSet: function fontSet(alias, className) {
    config.fontSets.push({
      alias: alias,
      fontSet: className || alias
    });
    return this;
  },

  /**
   * Specify a default style name associated with a font-icon library
   * fallback to Material Icons.
   *
   */
  defaultFontSet: function defaultFontSet(className) {
    config.defaultFontSet = !className ? '' : className;
    return this;
  },

  defaultIconSize: function defaultIconSize(iconSize) {
    config.defaultIconSize = iconSize;
    return this;
  },

  $get: ['$templateRequest', '$q', '$log', '$mdUtil', '$sce', function($templateRequest, $q, $log, $mdUtil, $sce) {
    return MdIconService(config, $templateRequest, $q, $log, $mdUtil, $sce);
  }]
};

/**
 *  Configuration item stored in the Icon registry; used for lookups
 *  to load if not already cached in the `loaded` cache
 */
function ConfigurationItem(url, viewBoxSize) {
  this.url = url;
  this.viewBoxSize = viewBoxSize || config.defaultViewBoxSize;
}

/**
 * @ngdoc service
 * @name $mdIcon
 * @module material.components.icon
 *
 * @description
 * The `$mdIcon` service is a function used to lookup SVG icons.
 *
 * @param {string} id Query value for a unique Id or URL. If the argument is a URL, then the service will retrieve the icon element
 * from its internal cache or load the icon and cache it first. If the value is not a URL-type string, then an ID lookup is
 * performed. The Id may be a unique icon ID or may include an iconSet ID prefix.
 *
 * For the **id** query to work properly, this means that all id-to-URL mappings must have been previously configured
 * using the `$mdIconProvider`.
 *
 * @returns {angular.$q.Promise} A promise that gets resolved to a clone of the initial SVG DOM element; which was
 * created from the SVG markup in the SVG data file. If an error occurs (e.g. the icon cannot be found) the promise
 * will get rejected.
 *
 * @usage
 * <hljs lang="js">
 * function SomeDirective($mdIcon) {
  *
  *   // See if the icon has already been loaded, if not
  *   // then lookup the icon from the registry cache, load and cache
  *   // it for future requests.
  *   // NOTE: ID queries require configuration with $mdIconProvider
  *
  *   $mdIcon('android').then(function(iconEl)    { element.append(iconEl); });
  *   $mdIcon('work:chair').then(function(iconEl) { element.append(iconEl); });
  *
  *   // Load and cache the external SVG using a URL
  *
  *   $mdIcon('img/icons/android.svg').then(function(iconEl) {
  *     element.append(iconEl);
  *   });
  * };
 * </hljs>
 *
 * > <b>Note:</b> The `<md-icon>` directive internally uses the `$mdIcon` service to query, loaded,
 *   and instantiate SVG DOM elements.
 */

/* @ngInject */
function MdIconService(config, $templateRequest, $q, $log, $mdUtil, $sce) {
  var iconCache = {};
  var svgCache = {};
  var urlRegex = /[-\w@:%\+.~#?&//=]{2,}\.[a-z]{2,4}\b(\/[-\w@:%\+.~#?&//=]*)?/i;
  var dataUrlRegex = /^data:image\/svg\+xml[\s*;\w\-\=]*?(base64)?,(.*)$/i;

  Icon.prototype = {clone: cloneSVG, prepare: prepareAndStyle};
  getIcon.fontSet = findRegisteredFontSet;

  // Publish service...
  return getIcon;

  /**
   * Actual $mdIcon service is essentially a lookup function
   */
  function getIcon(id) {
    id = id || '';

    // If the "id" provided is not a string, the only other valid value is a $sce trust wrapper
    // over a URL string. If the value is not trusted, this will intentionally throw an error
    // because the user is attempted to use an unsafe URL, potentially opening themselves up
    // to an XSS attack.
    if (!angular.isString(id)) {
      id = $sce.getTrustedUrl(id);
    }

    // If already loaded and cached, use a clone of the cached icon.
    // Otherwise either load by URL, or lookup in the registry and then load by URL, and cache.

    if (iconCache[id]) {
      return $q.when(transformClone(iconCache[id]));
    }

    if (urlRegex.test(id) || dataUrlRegex.test(id)) {
      return loadByURL(id).then(cacheIcon(id));
    }

    if (id.indexOf(':') == -1) {
      id = '$default:' + id;
    }

    var load = config[id] ? loadByID : loadFromIconSet;
    return load(id)
      .then(cacheIcon(id));
  }

  /**
   * Lookup registered fontSet style using its alias...
   * If not found,
   */
  function findRegisteredFontSet(alias) {
    var useDefault = angular.isUndefined(alias) || !(alias && alias.length);
    if (useDefault) return config.defaultFontSet;

    var result = alias;
    angular.forEach(config.fontSets, function(it) {
      if (it.alias == alias) result = it.fontSet || result;
    });

    return result;
  }

  function transformClone(cacheElement) {
    var clone = cacheElement.clone();
    var cacheSuffix = '_cache' + $mdUtil.nextUid();

    // We need to modify for each cached icon the id attributes.
    // This is needed because SVG id's are treated as normal DOM ids
    // and should not have a duplicated id.
    if (clone.id) clone.id += cacheSuffix;
    angular.forEach(clone.querySelectorAll('[id]'), function(item) {
      item.id += cacheSuffix;
    });

    return clone;
  }

  /**
   * Prepare and cache the loaded icon for the specified `id`
   */
  function cacheIcon(id) {

    return function updateCache(icon) {
      iconCache[id] = isIcon(icon) ? icon : new Icon(icon, config[id]);

      return iconCache[id].clone();
    };
  }

  /**
   * Lookup the configuration in the registry, if !registered throw an error
   * otherwise load the icon [on-demand] using the registered URL.
   *
   */
  function loadByID(id) {
    var iconConfig = config[id];
    return loadByURL(iconConfig.url).then(function(icon) {
      return new Icon(icon, iconConfig);
    });
  }

  /**
   *    Loads the file as XML and uses querySelector( <id> ) to find
   *    the desired node...
   */
  function loadFromIconSet(id) {
    var setName = id.substring(0, id.lastIndexOf(':')) || '$default';
    var iconSetConfig = config[setName];

    return !iconSetConfig ? announceIdNotFound(id) : loadByURL(iconSetConfig.url).then(extractFromSet);

    function extractFromSet(set) {
      var iconName = id.slice(id.lastIndexOf(':') + 1);
      var icon = set.querySelector('#' + iconName);
      return icon ? new Icon(icon, iconSetConfig) : announceIdNotFound(id);
    }

    function announceIdNotFound(id) {
      var msg = 'icon ' + id + ' not found';
      $log.warn(msg);

      return $q.reject(msg || id);
    }
  }

  /**
   * Load the icon by URL (may use the $templateCache).
   * Extract the data for later conversion to Icon
   */
  function loadByURL(url) {
    /* Load the icon from embedded data URL. */
    function loadByDataUrl(url) {
      var results = dataUrlRegex.exec(url);
      var isBase64 = /base64/i.test(url);
      var data = isBase64 ? window.atob(results[2]) : results[2];

      return $q.when(angular.element(data)[0]);
    }

    /* Load the icon by URL using HTTP. */
    function loadByHttpUrl(url) {
      return $q(function(resolve, reject) {
        // Catch HTTP or generic errors not related to incorrect icon IDs.
        var announceAndReject = function(err) {
            var msg = angular.isString(err) ? err : (err.message || err.data || err.statusText);
            $log.warn(msg);
            reject(err);
          },
          extractSvg = function(response) {
            if (!svgCache[url]) {
              svgCache[url] = angular.element('<div>').append(response)[0].querySelector('svg');
            }
            resolve(svgCache[url]);
          };

        $templateRequest(url, true).then(extractSvg, announceAndReject);
      });
    }

    return dataUrlRegex.test(url)
      ? loadByDataUrl(url)
      : loadByHttpUrl(url);
  }

  /**
   * Check target signature to see if it is an Icon instance.
   */
  function isIcon(target) {
    return angular.isDefined(target.element) && angular.isDefined(target.config);
  }

  /**
   *  Define the Icon class
   */
  function Icon(el, config) {
    if (el && el.tagName != 'svg') {
      el = angular.element('<svg xmlns="http://www.w3.org/2000/svg">').append(el.cloneNode(true))[0];
    }

    // Inject the namespace if not available...
    if (!el.getAttribute('xmlns')) {
      el.setAttribute('xmlns', "http://www.w3.org/2000/svg");
    }

    this.element = el;
    this.config = config;
    this.prepare();
  }

  /**
   *  Prepare the DOM element that will be cached in the
   *  loaded iconCache store.
   */
  function prepareAndStyle() {
    var viewBoxSize = this.config ? this.config.viewBoxSize : config.defaultViewBoxSize;
    angular.forEach({
      'fit': '',
      'height': '100%',
      'width': '100%',
      'preserveAspectRatio': 'xMidYMid meet',
      'viewBox': this.element.getAttribute('viewBox') || ('0 0 ' + viewBoxSize + ' ' + viewBoxSize),
      'focusable': false // Disable IE11s default behavior to make SVGs focusable
    }, function(val, attr) {
      this.element.setAttribute(attr, val);
    }, this);
  }

  /**
   * Clone the Icon DOM element.
   */
  function cloneSVG() {
    // If the element or any of its children have a style attribute, then a CSP policy without
    // 'unsafe-inline' in the style-src directive, will result in a violation.
    return this.element.cloneNode(true);
  }

}

})();
(function(){
"use strict";



MenuController.$inject = ["$mdMenu", "$attrs", "$element", "$scope", "$mdUtil", "$timeout", "$rootScope", "$q", "$log"];
angular
    .module('material.components.menu')
    .controller('mdMenuCtrl', MenuController);

/**
 * @ngInject
 */
function MenuController($mdMenu, $attrs, $element, $scope, $mdUtil, $timeout, $rootScope, $q, $log) {

  var prefixer = $mdUtil.prefixer();
  var menuContainer;
  var self = this;
  var triggerElement;

  this.nestLevel = parseInt($attrs.mdNestLevel, 10) || 0;

  /**
   * Called by our linking fn to provide access to the menu-content
   * element removed during link
   */
  this.init = function init(setMenuContainer, opts) {
    opts = opts || {};
    menuContainer = setMenuContainer;

    // Default element for ARIA attributes has the ngClick or ngMouseenter expression
    triggerElement = $element[0].querySelector(prefixer.buildSelector(['ng-click', 'ng-mouseenter']));
    triggerElement.setAttribute('aria-expanded', 'false');

    this.isInMenuBar = opts.isInMenuBar;
    this.nestedMenus = $mdUtil.nodesToArray(menuContainer[0].querySelectorAll('.md-nested-menu'));

    menuContainer.on('$mdInterimElementRemove', function() {
      self.isOpen = false;
      $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});
    });
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});

    var menuContainerId = 'menu_container_' + $mdUtil.nextUid();
    menuContainer.attr('id', menuContainerId);
    angular.element(triggerElement).attr({
      'aria-owns': menuContainerId,
      'aria-haspopup': 'true'
    });

    $scope.$on('$destroy', angular.bind(this, function() {
      this.disableHoverListener();
      $mdMenu.destroy();
    }));

    menuContainer.on('$destroy', function() {
      $mdMenu.destroy();
    });
  };

  var openMenuTimeout, menuItems, deregisterScopeListeners = [];
  this.enableHoverListener = function() {
    deregisterScopeListeners.push($rootScope.$on('$mdMenuOpen', function(event, el) {
      if (menuContainer[0].contains(el[0])) {
        self.currentlyOpenMenu = el.controller('mdMenu');
        self.isAlreadyOpening = false;
        self.currentlyOpenMenu.registerContainerProxy(self.triggerContainerProxy.bind(self));
      }
    }));
    deregisterScopeListeners.push($rootScope.$on('$mdMenuClose', function(event, el) {
      if (menuContainer[0].contains(el[0])) {
        self.currentlyOpenMenu = undefined;
      }
    }));
    menuItems = angular.element($mdUtil.nodesToArray(menuContainer[0].children[0].children));
    menuItems.on('mouseenter', self.handleMenuItemHover);
    menuItems.on('mouseleave', self.handleMenuItemMouseLeave);
  };

  this.disableHoverListener = function() {
    while (deregisterScopeListeners.length) {
      deregisterScopeListeners.shift()();
    }
    menuItems && menuItems.off('mouseenter', self.handleMenuItemHover);
    menuItems && menuItems.off('mouseleave', self.handleMenuItemMouseLeave);
  };

  this.handleMenuItemHover = function(event) {
    if (self.isAlreadyOpening) return;
    var nestedMenu = (
      event.target.querySelector('md-menu')
        || $mdUtil.getClosest(event.target, 'MD-MENU')
    );
    openMenuTimeout = $timeout(function() {
      if (nestedMenu) {
        nestedMenu = angular.element(nestedMenu).controller('mdMenu');
      }

      if (self.currentlyOpenMenu && self.currentlyOpenMenu != nestedMenu) {
        var closeTo = self.nestLevel + 1;
        self.currentlyOpenMenu.close(true, { closeTo: closeTo });
        self.isAlreadyOpening = !!nestedMenu;
        nestedMenu && nestedMenu.open();
      } else if (nestedMenu && !nestedMenu.isOpen && nestedMenu.open) {
        self.isAlreadyOpening = !!nestedMenu;
        nestedMenu && nestedMenu.open();
      }
    }, nestedMenu ? 100 : 250);
    var focusableTarget = event.currentTarget.querySelector('.md-button:not([disabled])');
    focusableTarget && focusableTarget.focus();
  };

  this.handleMenuItemMouseLeave = function() {
    if (openMenuTimeout) {
      $timeout.cancel(openMenuTimeout);
      openMenuTimeout = undefined;
    }
  };


  /**
   * Uses the $mdMenu interim element service to open the menu contents
   */
  this.open = function openMenu(ev) {
    ev && ev.stopPropagation();
    ev && ev.preventDefault();
    if (self.isOpen) return;
    self.enableHoverListener();
    self.isOpen = true;
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});
    triggerElement = triggerElement || (ev ? ev.target : $element[0]);
    triggerElement.setAttribute('aria-expanded', 'true');
    $scope.$emit('$mdMenuOpen', $element);
    $mdMenu.show({
      scope: $scope,
      mdMenuCtrl: self,
      nestLevel: self.nestLevel,
      element: menuContainer,
      target: triggerElement,
      preserveElement: true,
      parent: 'body'
    }).finally(function() {
      triggerElement.setAttribute('aria-expanded', 'false');
      self.disableHoverListener();
    });
  };

  this.onIsOpenChanged = function(isOpen) {
    if (isOpen) {
      menuContainer.attr('aria-hidden', 'false');
      $element[0].classList.add('md-open');
      angular.forEach(self.nestedMenus, function(el) {
        el.classList.remove('md-open');
      });
    } else {
      menuContainer.attr('aria-hidden', 'true');
      $element[0].classList.remove('md-open');
    }
    $scope.$mdMenuIsOpen = self.isOpen;
  };

  this.focusMenuContainer = function focusMenuContainer() {
    var focusTarget = menuContainer[0]
      .querySelector(prefixer.buildSelector(['md-menu-focus-target', 'md-autofocus']));

    if (!focusTarget) focusTarget = menuContainer[0].querySelector('.md-button:not([disabled])');
    focusTarget.focus();
  };

  this.registerContainerProxy = function registerContainerProxy(handler) {
    this.containerProxy = handler;
  };

  this.triggerContainerProxy = function triggerContainerProxy(ev) {
    this.containerProxy && this.containerProxy(ev);
  };

  this.destroy = function() {
    return self.isOpen ? $mdMenu.destroy() : $q.when(false);
  };

  // Use the $mdMenu interim element service to close the menu contents
  this.close = function closeMenu(skipFocus, closeOpts) {
    if ( !self.isOpen ) return;
    self.isOpen = false;
    $mdUtil.nextTick(function(){ self.onIsOpenChanged(self.isOpen);});

    var eventDetails = angular.extend({}, closeOpts, { skipFocus: skipFocus });
    $scope.$emit('$mdMenuClose', $element, eventDetails);
    $mdMenu.hide(null, closeOpts);

    if (!skipFocus) {
      var el = self.restoreFocusTo || $element.find('button')[0];
      if (el instanceof angular.element) el = el[0];
      if (el) el.focus();
    }
  };

  /**
   * Build a nice object out of our string attribute which specifies the
   * target mode for left and top positioning
   */
  this.positionMode = function positionMode() {
    var attachment = ($attrs.mdPositionMode || 'target').split(' ');

    // If attachment is a single item, duplicate it for our second value.
    // ie. 'target' -> 'target target'
    if (attachment.length == 1) {
      attachment.push(attachment[0]);
    }

    return {
      left: attachment[0],
      top: attachment[1]
    };
  };

  /**
   * Build a nice object out of our string attribute which specifies
   * the offset of top and left in pixels.
   */
  this.offsets = function offsets() {
    var position = ($attrs.mdOffset || '0 0').split(' ').map(parseFloat);
    if (position.length == 2) {
      return {
        left: position[0],
        top: position[1]
      };
    } else if (position.length == 1) {
      return {
        top: position[0],
        left: position[0]
      };
    } else {
      throw Error('Invalid offsets specified. Please follow format <x, y> or <n>');
    }
  };

  // Functionality that is exposed in the view.
  $scope.$mdMenu = {
    open: this.open,
    close: this.close
  };

  // Deprecated APIs
  $scope.$mdOpenMenu = angular.bind(this, function() {
    $log.warn('mdMenu: The $mdOpenMenu method is deprecated. Please use `$mdMenu.open`.');
    return this.open.apply(this, arguments);
  });
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdMenu
 * @module material.components.menu
 * @restrict E
 * @description
 *
 * Menus are elements that open when clicked. They are useful for displaying
 * additional options within the context of an action.
 *
 * Every `md-menu` must specify exactly two child elements. The first element is what is
 * left in the DOM and is used to open the menu. This element is called the trigger element.
 * The trigger element's scope has access to `$mdMenu.open($event)`
 * which it may call to open the menu. By passing $event as argument, the
 * corresponding event is stopped from propagating up the DOM-tree. Similarly, `$mdMenu.close()`
 * can be used to close the menu.
 *
 * The second element is the `md-menu-content` element which represents the
 * contents of the menu when it is open. Typically this will contain `md-menu-item`s,
 * but you can do custom content as well.
 *
 * <hljs lang="html">
 * <md-menu>
 *  <!-- Trigger element is a md-button with an icon -->
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button" aria-label="Open sample menu">
 *    <md-icon md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item><md-button ng-click="doSomething()">Do Something</md-button></md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>

 * ## Sizing Menus
 *
 * The width of the menu when it is open may be specified by specifying a `width`
 * attribute on the `md-menu-content` element.
 * See the [Material Design Spec](https://material.google.com/components/menus.html#menus-simple-menus)
 * for more information.
 *
 *
 * ## Aligning Menus
 *
 * When a menu opens, it is important that the content aligns with the trigger element.
 * Failure to align menus can result in jarring experiences for users as content
 * suddenly shifts. To help with this, `md-menu` provides serveral APIs to help
 * with alignment.
 *
 * ### Target Mode
 *
 * By default, `md-menu` will attempt to align the `md-menu-content` by aligning
 * designated child elements in both the trigger and the menu content.
 *
 * To specify the alignment element in the `trigger` you can use the `md-menu-origin`
 * attribute on a child element. If no `md-menu-origin` is specified, the `md-menu`
 * will be used as the origin element.
 *
 * Similarly, the `md-menu-content` may specify a `md-menu-align-target` for a
 * `md-menu-item` to specify the node that it should try and align with.
 *
 * In this example code, we specify an icon to be our origin element, and an
 * icon in our menu content to be our alignment target. This ensures that both
 * icons are aligned when the menu opens.
 *
 * <hljs lang="html">
 * <md-menu>
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button" aria-label="Open some menu">
 *    <md-icon md-menu-origin md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item>
 *      <md-button ng-click="doSomething()" aria-label="Do something">
 *        <md-icon md-menu-align-target md-svg-icon="call:phone"></md-icon>
 *        Do Something
 *      </md-button>
 *    </md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>
 *
 * Sometimes we want to specify alignment on the right side of an element, for example
 * if we have a menu on the right side a toolbar, we want to right align our menu content.
 *
 * We can specify the origin by using the `md-position-mode` attribute on both
 * the `x` and `y` axis. Right now only the `x-axis` has more than one option.
 * You may specify the default mode of `target target` or
 * `target-right target` to specify a right-oriented alignment target. See the
 * position section of the demos for more examples.
 *
 * ### Menu Offsets
 *
 * It is sometimes unavoidable to need to have a deeper level of control for
 * the positioning of a menu to ensure perfect alignment. `md-menu` provides
 * the `md-offset` attribute to allow pixel level specificty of adjusting the
 * exact positioning.
 *
 * This offset is provided in the format of `x y` or `n` where `n` will be used
 * in both the `x` and `y` axis.
 *
 * For example, to move a menu by `2px` down from the top, we can use:
 * <hljs lang="html">
 * <md-menu md-offset="0 2">
 *   <!-- menu-content -->
 * </md-menu>
 * </hljs>
 *
 * ### Auto Focus
 * By default, when a menu opens, `md-menu` focuses the first button in the menu content.
 *
 * But sometimes you would like to focus another specific menu item instead of the first.<br/>
 * This can be done by applying the `md-autofocus` directive on the given element.
 *
 * <hljs lang="html">
 * <md-menu-item>
 *   <md-button md-autofocus ng-click="doSomething()">
 *     Auto Focus
 *   </md-button>
 * </md-menu-item>
 * </hljs>
 *
 *
 * ### Preventing close
 *
 * Sometimes you would like to be able to click on a menu item without having the menu
 * close. To do this, AngularJS Material exposes the `md-prevent-menu-close` attribute which
 * can be added to a button inside a menu to stop the menu from automatically closing.
 * You can then close the menu either by using `$mdMenu.close()` in the template,
 * or programatically by injecting `$mdMenu` and calling `$mdMenu.hide()`.
 *
 * <hljs lang="html">
 * <md-menu-content ng-mouseleave="$mdMenu.close()">
 *   <md-menu-item>
 *     <md-button ng-click="doSomething()" aria-label="Do something" md-prevent-menu-close="md-prevent-menu-close">
 *       <md-icon md-menu-align-target md-svg-icon="call:phone"></md-icon>
 *       Do Something
 *     </md-button>
 *   </md-menu-item>
 * </md-menu-content>
 * </hljs>
 *
 * @usage
 * <hljs lang="html">
 * <md-menu>
 *  <md-button ng-click="$mdMenu.open($event)" class="md-icon-button">
 *    <md-icon md-svg-icon="call:phone"></md-icon>
 *  </md-button>
 *  <md-menu-content>
 *    <md-menu-item><md-button ng-click="doSomething()">Do Something</md-button></md-menu-item>
 *  </md-menu-content>
 * </md-menu>
 * </hljs>
 *
 * @param {string} md-position-mode The position mode in the form of
 *           `x`, `y`. Default value is `target`,`target`. Right now the `x` axis
 *           also supports `target-right`.
 * @param {string} md-offset An offset to apply to the dropdown after positioning
 *           `x`, `y`. Default value is `0`,`0`.
 *
 */

MenuDirective.$inject = ["$mdUtil"];
angular
    .module('material.components.menu')
    .directive('mdMenu', MenuDirective);

/**
 * @ngInject
 */
function MenuDirective($mdUtil) {
  var INVALID_PREFIX = 'Invalid HTML for md-menu: ';
  return {
    restrict: 'E',
    require: ['mdMenu', '?^mdMenuBar'],
    controller: 'mdMenuCtrl', // empty function to be built by link
    scope: true,
    compile: compile
  };

  function compile(templateElement) {
    templateElement.addClass('md-menu');

    var triggerEl = templateElement.children()[0];
    var contentEl = templateElement.children()[1];

    var prefixer = $mdUtil.prefixer();

    if (!prefixer.hasAttribute(triggerEl, 'ng-click')) {
      triggerEl = triggerEl
          .querySelector(prefixer.buildSelector(['ng-click', 'ng-mouseenter'])) || triggerEl;
    }

    var isButtonTrigger = triggerEl.nodeName === 'MD-BUTTON' || triggerEl.nodeName === 'BUTTON';

    if (triggerEl && isButtonTrigger && !triggerEl.hasAttribute('type')) {
      triggerEl.setAttribute('type', 'button');
    }

    if (!triggerEl) {
      throw Error(INVALID_PREFIX + 'Expected the menu to have a trigger element.');
    }

    if (!contentEl || contentEl.nodeName !== 'MD-MENU-CONTENT') {
      throw Error(INVALID_PREFIX + 'Expected the menu to contain a `md-menu-content` element.');
    }

    // Default element for ARIA attributes has the ngClick or ngMouseenter expression
    triggerEl && triggerEl.setAttribute('aria-haspopup', 'true');

    var nestedMenus = templateElement[0].querySelectorAll('md-menu');
    var nestingDepth = parseInt(templateElement[0].getAttribute('md-nest-level'), 10) || 0;
    if (nestedMenus) {
      angular.forEach($mdUtil.nodesToArray(nestedMenus), function(menuEl) {
        if (!menuEl.hasAttribute('md-position-mode')) {
          menuEl.setAttribute('md-position-mode', 'cascade');
        }
        menuEl.classList.add('_md-nested-menu');
        menuEl.setAttribute('md-nest-level', nestingDepth + 1);
      });
    }
    return link;
  }

  function link(scope, element, attr, ctrls) {
    var mdMenuCtrl = ctrls[0];
    var isInMenuBar = !!ctrls[1];
    // Move everything into a md-menu-container and pass it to the controller
    var menuContainer = angular.element( '<div class="_md md-open-menu-container md-whiteframe-z2"></div>');
    var menuContents = element.children()[1];

    element.addClass('_md');     // private md component indicator for styling

    if (!menuContents.hasAttribute('role')) {
      menuContents.setAttribute('role', 'menu');
    }
    menuContainer.append(menuContents);

    element.on('$destroy', function() {
      menuContainer.remove();
    });

    element.append(menuContainer);
    menuContainer[0].style.display = 'none';
    mdMenuCtrl.init(menuContainer, { isInMenuBar: isInMenuBar });

  }
}

})();
(function(){
"use strict";


MenuProvider.$inject = ["$$interimElementProvider"];angular
  .module('material.components.menu')
  .provider('$mdMenu', MenuProvider);

/*
 * Interim element provider for the menu.
 * Handles behavior for a menu while it is open, including:
 *    - handling animating the menu opening/closing
 *    - handling key/mouse events on the menu element
 *    - handling enabling/disabling scroll while the menu is open
 *    - handling redrawing during resizes and orientation changes
 *
 */

function MenuProvider($$interimElementProvider) {
  menuDefaultOptions.$inject = ["$mdUtil", "$mdTheming", "$mdConstant", "$document", "$window", "$q", "$$rAF", "$animateCss", "$animate", "$log"];
  var MENU_EDGE_MARGIN = 8;

  return $$interimElementProvider('$mdMenu')
    .setDefaults({
      methods: ['target'],
      options: menuDefaultOptions
    });

  /* @ngInject */
  function menuDefaultOptions($mdUtil, $mdTheming, $mdConstant, $document, $window, $q, $$rAF,
                              $animateCss, $animate, $log) {

    var prefixer = $mdUtil.prefixer();
    var animator = $mdUtil.dom.animator;

    return {
      parent: 'body',
      onShow: onShow,
      onRemove: onRemove,
      hasBackdrop: true,
      disableParentScroll: true,
      skipCompile: true,
      preserveScope: true,
      multiple: true,
      themable: true
    };

    /**
     * Show modal backdrop element...
     * @returns {function(): void} A function that removes this backdrop
     */
    function showBackdrop(scope, element, options) {
      if (options.nestLevel) return angular.noop;

      // If we are not within a dialog...
      if (options.disableParentScroll && !$mdUtil.getClosest(options.target, 'MD-DIALOG')) {
        // !! DO this before creating the backdrop; since disableScrollAround()
        //    configures the scroll offset; which is used by mdBackDrop postLink()
        options.restoreScroll = $mdUtil.disableScrollAround(options.element, options.parent);
      } else {
        options.disableParentScroll = false;
      }

      if (options.hasBackdrop) {
        options.backdrop = $mdUtil.createBackdrop(scope, "md-menu-backdrop md-click-catcher");

        $animate.enter(options.backdrop, $document[0].body);
      }

      /**
       * Hide and destroys the backdrop created by showBackdrop()
       */
      return function hideBackdrop() {
        if (options.backdrop) options.backdrop.remove();
        if (options.disableParentScroll) options.restoreScroll();
      };
    }

    /**
     * Removing the menu element from the DOM and remove all associated event listeners
     * and backdrop
     */
    function onRemove(scope, element, opts) {
      opts.cleanupInteraction();
      opts.cleanupBackdrop();
      opts.cleanupResizing();
      opts.hideBackdrop();

      // Before the menu is closing remove the clickable class.
      element.removeClass('md-clickable');

      // For navigation $destroy events, do a quick, non-animated removal,
      // but for normal closes (from clicks, etc) animate the removal

      return (opts.$destroy === true) ? detachAndClean() : animateRemoval().then( detachAndClean );

      /**
       * For normal closes, animate the removal.
       * For forced closes (like $destroy events), skip the animations
       */
      function animateRemoval() {
        return $animateCss(element, {addClass: 'md-leave'}).start();
      }

      /**
       * Detach the element
       */
      function detachAndClean() {
        element.removeClass('md-active');
        detachElement(element, opts);
        opts.alreadyOpen = false;
      }

    }

    /**
     * Inserts and configures the staged Menu element into the DOM, positioning it,
     * and wiring up various interaction events
     */
    function onShow(scope, element, opts) {
      sanitizeAndConfigure(opts);

      if (opts.menuContentEl[0]) {
        // Inherit the theme from the target element.
        $mdTheming.inherit(opts.menuContentEl, opts.target);
      } else {
        $log.warn(
          '$mdMenu: Menu elements should always contain a `md-menu-content` element,' +
          'otherwise interactivity features will not work properly.',
          element
        );
      }
      
      // Register various listeners to move menu on resize/orientation change
      opts.cleanupResizing = startRepositioningOnResize();
      opts.hideBackdrop = showBackdrop(scope, element, opts);

      // Return the promise for when our menu is done animating in
      return showMenu()
        .then(function(response) {
          opts.alreadyOpen = true;
          opts.cleanupInteraction = activateInteraction();
          opts.cleanupBackdrop = setupBackdrop();

          // Since the menu finished its animation, mark the menu as clickable.
          element.addClass('md-clickable');

          return response;
        });

      /**
       * Place the menu into the DOM and call positioning related functions
       */
      function showMenu() {
        opts.parent.append(element);
        element[0].style.display = '';

        return $q(function(resolve) {
          var position = calculateMenuPosition(element, opts);

          element.removeClass('md-leave');

          // Animate the menu scaling, and opacity [from its position origin (default == top-left)]
          // to normal scale.
          $animateCss(element, {
            addClass: 'md-active',
            from: animator.toCss(position),
            to: animator.toCss({transform: ''})
          })
          .start()
          .then(resolve);

        });
      }

      /**
       * Check for valid opts and set some sane defaults
       */
      function sanitizeAndConfigure() {
        if (!opts.target) {
          throw Error(
            '$mdMenu.show() expected a target to animate from in options.target'
          );
        }
        angular.extend(opts, {
          alreadyOpen: false,
          isRemoved: false,
          target: angular.element(opts.target), //make sure it's not a naked dom node
          parent: angular.element(opts.parent),
          menuContentEl: angular.element(element[0].querySelector('md-menu-content'))
        });
      }

      /**
       * Configure various resize listeners for screen changes
       */
      function startRepositioningOnResize() {

        var repositionMenu = (function(target, options) {
          return $$rAF.throttle(function() {
            if (opts.isRemoved) return;
            var position = calculateMenuPosition(target, options);

            target.css(animator.toCss(position));
          });
        })(element, opts);

        $window.addEventListener('resize', repositionMenu);
        $window.addEventListener('orientationchange', repositionMenu);

        return function stopRepositioningOnResize() {

          // Disable resizing handlers
          $window.removeEventListener('resize', repositionMenu);
          $window.removeEventListener('orientationchange', repositionMenu);

        };
      }

      /**
       * Sets up the backdrop and listens for click elements.
       * Once the backdrop will be clicked, the menu will automatically close.
       * @returns {!Function} Function to remove the backdrop.
       */
      function setupBackdrop() {
        if (!opts.backdrop) return angular.noop;

        opts.backdrop.on('click', onBackdropClick);

        return function() {
          opts.backdrop.off('click', onBackdropClick);
        }
      }

      /**
       * Function to be called whenever the backdrop is clicked.
       * @param {!MouseEvent} event
       */
      function onBackdropClick(event) {
        event.preventDefault();
        event.stopPropagation();

        scope.$apply(function() {
          opts.mdMenuCtrl.close(true, { closeAll: true });
        });
      }

      /**
       * Activate interaction on the menu. Resolves the focus target and closes the menu on
       * escape or option click.
       * @returns {!Function} Function to deactivate the interaction listeners.
       */
      function activateInteraction() {
        if (!opts.menuContentEl[0]) return angular.noop;

        // Wire up keyboard listeners.
        // - Close on escape,
        // - focus next item on down arrow,
        // - focus prev item on up
        opts.menuContentEl.on('keydown', onMenuKeyDown);
        opts.menuContentEl[0].addEventListener('click', captureClickListener, true);

        // kick off initial focus in the menu on the first enabled element
        var focusTarget = opts.menuContentEl[0]
          .querySelector(prefixer.buildSelector(['md-menu-focus-target', 'md-autofocus']));

        if ( !focusTarget ) {
          var childrenLen = opts.menuContentEl[0].children.length;
          for(var childIndex = 0; childIndex < childrenLen; childIndex++) {
            var child = opts.menuContentEl[0].children[childIndex];
            focusTarget = child.querySelector('.md-button:not([disabled])');
            if (focusTarget) {
              break;
            }
            if (child.firstElementChild && !child.firstElementChild.disabled) {
              focusTarget = child.firstElementChild;
              break;
            }
          }
        }

        focusTarget && focusTarget.focus();

        return function cleanupInteraction() {
          opts.menuContentEl.off('keydown', onMenuKeyDown);
          opts.menuContentEl[0].removeEventListener('click', captureClickListener, true);
        };

        // ************************************
        // internal functions
        // ************************************

        function onMenuKeyDown(ev) {
          var handled;
          switch (ev.keyCode) {
            case $mdConstant.KEY_CODE.ESCAPE:
              opts.mdMenuCtrl.close(false, { closeAll: true });
              handled = true;
              break;
            case $mdConstant.KEY_CODE.UP_ARROW:
              if (!focusMenuItem(ev, opts.menuContentEl, opts, -1) && !opts.nestLevel) {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.DOWN_ARROW:
              if (!focusMenuItem(ev, opts.menuContentEl, opts, 1) && !opts.nestLevel) {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.LEFT_ARROW:
              if (opts.nestLevel) {
                opts.mdMenuCtrl.close();
              } else {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
            case $mdConstant.KEY_CODE.RIGHT_ARROW:
              var parentMenu = $mdUtil.getClosest(ev.target, 'MD-MENU');
              if (parentMenu && parentMenu != opts.parent[0]) {
                ev.target.click();
              } else {
                opts.mdMenuCtrl.triggerContainerProxy(ev);
              }
              handled = true;
              break;
          }
          if (handled) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
          }
        }

        function onBackdropClick(e) {
          e.preventDefault();
          e.stopPropagation();
          scope.$apply(function() {
            opts.mdMenuCtrl.close(true, { closeAll: true });
          });
        }

        // Close menu on menu item click, if said menu-item is not disabled
        function captureClickListener(e) {
          var target = e.target;
          // Traverse up the event until we get to the menuContentEl to see if
          // there is an ng-click and that the ng-click is not disabled
          do {
            if (target == opts.menuContentEl[0]) return;
            if ((hasAnyAttribute(target, ['ng-click', 'ng-href', 'ui-sref']) ||
                target.nodeName == 'BUTTON' || target.nodeName == 'MD-BUTTON') && !hasAnyAttribute(target, ['md-prevent-menu-close'])) {
              var closestMenu = $mdUtil.getClosest(target, 'MD-MENU');
              if (!target.hasAttribute('disabled') && (!closestMenu || closestMenu == opts.parent[0])) {
                close();
              }
              break;
            }
          } while (target = target.parentNode);

          function close() {
            scope.$apply(function() {
              opts.mdMenuCtrl.close(true, { closeAll: true });
            });
          }

          function hasAnyAttribute(target, attrs) {
            if (!target) return false;

            for (var i = 0, attr; attr = attrs[i]; ++i) {
              if (prefixer.hasAttribute(target, attr)) {
                return true;
              }
            }

            return false;
          }
        }

      }
    }

    /**
     * Takes a keypress event and focuses the next/previous menu
     * item from the emitting element
     * @param {event} e - The origin keypress event
     * @param {angular.element} menuEl - The menu element
     * @param {object} opts - The interim element options for the mdMenu
     * @param {number} direction - The direction to move in (+1 = next, -1 = prev)
     */
    function focusMenuItem(e, menuEl, opts, direction) {
      var currentItem = $mdUtil.getClosest(e.target, 'MD-MENU-ITEM');

      var items = $mdUtil.nodesToArray(menuEl[0].children);
      var currentIndex = items.indexOf(currentItem);

      // Traverse through our elements in the specified direction (+/-1) and try to
      // focus them until we find one that accepts focus
      var didFocus;
      for (var i = currentIndex + direction; i >= 0 && i < items.length; i = i + direction) {
        var focusTarget = items[i].querySelector('.md-button');
        didFocus = attemptFocus(focusTarget);
        if (didFocus) {
          break;
        }
      }
      return didFocus;
    }

    /**
     * Attempts to focus an element. Checks whether that element is the currently
     * focused element after attempting.
     * @param {HTMLElement} el - the element to attempt focus on
     * @returns {bool} - whether the element was successfully focused
     */
    function attemptFocus(el) {
      if (el && el.getAttribute('tabindex') != -1) {
        el.focus();
        return ($document[0].activeElement == el);
      }
    }

    /**
     * Use browser to remove this element without triggering a $destroy event
     */
    function detachElement(element, opts) {
      if (!opts.preserveElement) {
        if (toNode(element).parentNode === toNode(opts.parent)) {
          toNode(opts.parent).removeChild(toNode(element));
        }
      } else {
        toNode(element).style.display = 'none';
      }
    }

    /**
     * Computes menu position and sets the style on the menu container
     * @param {HTMLElement} el - the menu container element
     * @param {object} opts - the interim element options object
     */
    function calculateMenuPosition(el, opts) {

      var containerNode = el[0],
        openMenuNode = el[0].firstElementChild,
        openMenuNodeRect = openMenuNode.getBoundingClientRect(),
        boundryNode = $document[0].body,
        boundryNodeRect = boundryNode.getBoundingClientRect();

      var menuStyle = $window.getComputedStyle(openMenuNode);

      var originNode = opts.target[0].querySelector(prefixer.buildSelector('md-menu-origin')) || opts.target[0],
        originNodeRect = originNode.getBoundingClientRect();

      var bounds = {
        left: boundryNodeRect.left + MENU_EDGE_MARGIN,
        top: Math.max(boundryNodeRect.top, 0) + MENU_EDGE_MARGIN,
        bottom: Math.max(boundryNodeRect.bottom, Math.max(boundryNodeRect.top, 0) + boundryNodeRect.height) - MENU_EDGE_MARGIN,
        right: boundryNodeRect.right - MENU_EDGE_MARGIN
      };

      var alignTarget, alignTargetRect = { top:0, left : 0, right:0, bottom:0 }, existingOffsets  = { top:0, left : 0, right:0, bottom:0  };
      var positionMode = opts.mdMenuCtrl.positionMode();

      if (positionMode.top == 'target' || positionMode.left == 'target' || positionMode.left == 'target-right') {
        alignTarget = firstVisibleChild();
        if ( alignTarget ) {
          // TODO: Allow centering on an arbitrary node, for now center on first menu-item's child
          alignTarget = alignTarget.firstElementChild || alignTarget;
          alignTarget = alignTarget.querySelector(prefixer.buildSelector('md-menu-align-target')) || alignTarget;
          alignTargetRect = alignTarget.getBoundingClientRect();

          existingOffsets = {
            top: parseFloat(containerNode.style.top || 0),
            left: parseFloat(containerNode.style.left || 0)
          };
        }
      }

      var position = {};
      var transformOrigin = 'top ';

      switch (positionMode.top) {
        case 'target':
          position.top = existingOffsets.top + originNodeRect.top - alignTargetRect.top;
          break;
        case 'cascade':
          position.top = originNodeRect.top - parseFloat(menuStyle.paddingTop) - originNode.style.top;
          break;
        case 'bottom':
          position.top = originNodeRect.top + originNodeRect.height;
          break;
        default:
          throw new Error('Invalid target mode "' + positionMode.top + '" specified for md-menu on Y axis.');
      }

      var rtl = ($mdUtil.bidi() == 'rtl');

      switch (positionMode.left) {
        case 'target':
          position.left = existingOffsets.left + originNodeRect.left - alignTargetRect.left;
          transformOrigin += rtl ? 'right'  : 'left';
          break;
        case 'target-left':
          position.left = originNodeRect.left;
          transformOrigin += 'left';
          break;
        case 'target-right':
          position.left = originNodeRect.right - openMenuNodeRect.width + (openMenuNodeRect.right - alignTargetRect.right);
          transformOrigin += 'right';
          break;
        case 'cascade':
          var willFitRight = rtl ? (originNodeRect.left - openMenuNodeRect.width) < bounds.left : (originNodeRect.right + openMenuNodeRect.width) < bounds.right;
          position.left = willFitRight ? originNodeRect.right - originNode.style.left : originNodeRect.left - originNode.style.left - openMenuNodeRect.width;
          transformOrigin += willFitRight ? 'left' : 'right';
          break;
        case 'right':
          if (rtl) {
            position.left = originNodeRect.right - originNodeRect.width;
            transformOrigin += 'left';
          } else {
            position.left = originNodeRect.right - openMenuNodeRect.width;
            transformOrigin += 'right';
          }
          break;
        case 'left':
          if (rtl) {
            position.left = originNodeRect.right - openMenuNodeRect.width;
            transformOrigin += 'right';
          } else {
            position.left = originNodeRect.left;
            transformOrigin += 'left';
          }
          break;
        default:
          throw new Error('Invalid target mode "' + positionMode.left + '" specified for md-menu on X axis.');
      }

      var offsets = opts.mdMenuCtrl.offsets();
      position.top += offsets.top;
      position.left += offsets.left;

      clamp(position);

      var scaleX = Math.round(100 * Math.min(originNodeRect.width / containerNode.offsetWidth, 1.0)) / 100;
      var scaleY = Math.round(100 * Math.min(originNodeRect.height / containerNode.offsetHeight, 1.0)) / 100;

      return {
        top: Math.round(position.top),
        left: Math.round(position.left),
        // Animate a scale out if we aren't just repositioning
        transform: !opts.alreadyOpen ? $mdUtil.supplant('scale({0},{1})', [scaleX, scaleY]) : undefined,
        transformOrigin: transformOrigin
      };

      /**
       * Clamps the repositioning of the menu within the confines of
       * bounding element (often the screen/body)
       */
      function clamp(pos) {
        pos.top = Math.max(Math.min(pos.top, bounds.bottom - containerNode.offsetHeight), bounds.top);
        pos.left = Math.max(Math.min(pos.left, bounds.right - containerNode.offsetWidth), bounds.left);
      }

      /**
       * Gets the first visible child in the openMenuNode
       * Necessary incase menu nodes are being dynamically hidden
       */
      function firstVisibleChild() {
        for (var i = 0; i < openMenuNode.children.length; ++i) {
          if ($window.getComputedStyle(openMenuNode.children[i]).display != 'none') {
            return openMenuNode.children[i];
          }
        }
      }
    }
  }
  function toNode(el) {
    if (el instanceof angular.element) {
      el = el[0];
    }
    return el;
  }
}

})();
(function(){
"use strict";


MenuBarController.$inject = ["$scope", "$rootScope", "$element", "$attrs", "$mdConstant", "$document", "$mdUtil", "$timeout"];
angular
  .module('material.components.menuBar')
  .controller('MenuBarController', MenuBarController);

var BOUND_MENU_METHODS = ['handleKeyDown', 'handleMenuHover', 'scheduleOpenHoveredMenu', 'cancelScheduledOpen'];

/**
 * @ngInject
 */
function MenuBarController($scope, $rootScope, $element, $attrs, $mdConstant, $document, $mdUtil, $timeout) {
  this.$element = $element;
  this.$attrs = $attrs;
  this.$mdConstant = $mdConstant;
  this.$mdUtil = $mdUtil;
  this.$document = $document;
  this.$scope = $scope;
  this.$rootScope = $rootScope;
  this.$timeout = $timeout;

  var self = this;
  angular.forEach(BOUND_MENU_METHODS, function(methodName) {
    self[methodName] = angular.bind(self, self[methodName]);
  });
}

MenuBarController.prototype.init = function() {
  var $element = this.$element;
  var $mdUtil = this.$mdUtil;
  var $scope = this.$scope;

  var self = this;
  var deregisterFns = [];
  $element.on('keydown', this.handleKeyDown);
  this.parentToolbar = $mdUtil.getClosest($element, 'MD-TOOLBAR');

  deregisterFns.push(this.$rootScope.$on('$mdMenuOpen', function(event, el) {
    if (self.getMenus().indexOf(el[0]) != -1) {
      $element[0].classList.add('md-open');
      el[0].classList.add('md-open');
      self.currentlyOpenMenu = el.controller('mdMenu');
      self.currentlyOpenMenu.registerContainerProxy(self.handleKeyDown);
      self.enableOpenOnHover();
    }
  }));

  deregisterFns.push(this.$rootScope.$on('$mdMenuClose', function(event, el, opts) {
    var rootMenus = self.getMenus();
    if (rootMenus.indexOf(el[0]) != -1) {
      $element[0].classList.remove('md-open');
      el[0].classList.remove('md-open');
    }

    if ($element[0].contains(el[0])) {
      var parentMenu = el[0];
      while (parentMenu && rootMenus.indexOf(parentMenu) == -1) {
        parentMenu = $mdUtil.getClosest(parentMenu, 'MD-MENU', true);
      }
      if (parentMenu) {
        if (!opts.skipFocus) parentMenu.querySelector('button:not([disabled])').focus();
        self.currentlyOpenMenu = undefined;
        self.disableOpenOnHover();
        self.setKeyboardMode(true);
      }
    }
  }));

  $scope.$on('$destroy', function() {
    self.disableOpenOnHover();
    while (deregisterFns.length) {
      deregisterFns.shift()();
    }
  });


  this.setKeyboardMode(true);
};

MenuBarController.prototype.setKeyboardMode = function(enabled) {
  if (enabled) this.$element[0].classList.add('md-keyboard-mode');
  else this.$element[0].classList.remove('md-keyboard-mode');
};

MenuBarController.prototype.enableOpenOnHover = function() {
  if (this.openOnHoverEnabled) return;

  var self = this;

  self.openOnHoverEnabled = true;

  if (self.parentToolbar) {
    self.parentToolbar.classList.add('md-has-open-menu');

    // Needs to be on the next tick so it doesn't close immediately.
    self.$mdUtil.nextTick(function() {
      angular.element(self.parentToolbar).on('click', self.handleParentClick);
    }, false);
  }

  angular
    .element(self.getMenus())
    .on('mouseenter', self.handleMenuHover);
};

MenuBarController.prototype.handleMenuHover = function(e) {
  this.setKeyboardMode(false);
  if (this.openOnHoverEnabled) {
    this.scheduleOpenHoveredMenu(e);
  }
};

MenuBarController.prototype.disableOpenOnHover = function() {
  if (!this.openOnHoverEnabled) return;

  this.openOnHoverEnabled = false;

  if (this.parentToolbar) {
    this.parentToolbar.classList.remove('md-has-open-menu');
    angular.element(this.parentToolbar).off('click', this.handleParentClick);
  }

  angular
    .element(this.getMenus())
    .off('mouseenter', this.handleMenuHover);
};

MenuBarController.prototype.scheduleOpenHoveredMenu = function(e) {
  var menuEl = angular.element(e.currentTarget);
  var menuCtrl = menuEl.controller('mdMenu');
  this.setKeyboardMode(false);
  this.scheduleOpenMenu(menuCtrl);
};

MenuBarController.prototype.scheduleOpenMenu = function(menuCtrl) {
  var self = this;
  var $timeout = this.$timeout;
  if (menuCtrl != self.currentlyOpenMenu) {
    $timeout.cancel(self.pendingMenuOpen);
    self.pendingMenuOpen = $timeout(function() {
      self.pendingMenuOpen = undefined;
      if (self.currentlyOpenMenu) {
        self.currentlyOpenMenu.close(true, { closeAll: true });
      }
      menuCtrl.open();
    }, 200, false);
  }
};

MenuBarController.prototype.handleKeyDown = function(e) {
  var keyCodes = this.$mdConstant.KEY_CODE;
  var currentMenu = this.currentlyOpenMenu;
  var wasOpen = currentMenu && currentMenu.isOpen;
  this.setKeyboardMode(true);
  var handled, newMenu, newMenuCtrl;
  switch (e.keyCode) {
    case keyCodes.DOWN_ARROW:
      if (currentMenu) {
        currentMenu.focusMenuContainer();
      } else {
        this.openFocusedMenu();
      }
      handled = true;
      break;
    case keyCodes.UP_ARROW:
      currentMenu && currentMenu.close();
      handled = true;
      break;
    case keyCodes.LEFT_ARROW:
      newMenu = this.focusMenu(-1);
      if (wasOpen) {
        newMenuCtrl = angular.element(newMenu).controller('mdMenu');
        this.scheduleOpenMenu(newMenuCtrl);
      }
      handled = true;
      break;
    case keyCodes.RIGHT_ARROW:
      newMenu = this.focusMenu(+1);
      if (wasOpen) {
        newMenuCtrl = angular.element(newMenu).controller('mdMenu');
        this.scheduleOpenMenu(newMenuCtrl);
      }
      handled = true;
      break;
  }
  if (handled) {
    e && e.preventDefault && e.preventDefault();
    e && e.stopImmediatePropagation && e.stopImmediatePropagation();
  }
};

MenuBarController.prototype.focusMenu = function(direction) {
  var menus = this.getMenus();
  var focusedIndex = this.getFocusedMenuIndex();

  if (focusedIndex == -1) { focusedIndex = this.getOpenMenuIndex(); }

  var changed = false;

  if (focusedIndex == -1) { focusedIndex = 0; changed = true; }
  else if (
    direction < 0 && focusedIndex > 0 ||
    direction > 0 && focusedIndex < menus.length - direction
  ) {
    focusedIndex += direction;
    changed = true;
  }
  if (changed) {
    menus[focusedIndex].querySelector('button').focus();
    return menus[focusedIndex];
  }
};

MenuBarController.prototype.openFocusedMenu = function() {
  var menu = this.getFocusedMenu();
  menu && angular.element(menu).controller('mdMenu').open();
};

MenuBarController.prototype.getMenus = function() {
  var $element = this.$element;
  return this.$mdUtil.nodesToArray($element[0].children)
    .filter(function(el) { return el.nodeName == 'MD-MENU'; });
};

MenuBarController.prototype.getFocusedMenu = function() {
  return this.getMenus()[this.getFocusedMenuIndex()];
};

MenuBarController.prototype.getFocusedMenuIndex = function() {
  var $mdUtil = this.$mdUtil;
  var focusedEl = $mdUtil.getClosest(
    this.$document[0].activeElement,
    'MD-MENU'
  );
  if (!focusedEl) return -1;

  var focusedIndex = this.getMenus().indexOf(focusedEl);
  return focusedIndex;
};

MenuBarController.prototype.getOpenMenuIndex = function() {
  var menus = this.getMenus();
  for (var i = 0; i < menus.length; ++i) {
    if (menus[i].classList.contains('md-open')) return i;
  }
  return -1;
};

MenuBarController.prototype.handleParentClick = function(event) {
  var openMenu = this.querySelector('md-menu.md-open');

  if (openMenu && !openMenu.contains(event.target)) {
    angular.element(openMenu).controller('mdMenu').close(true, {
      closeAll: true
    });
  }
};

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdMenuBar
 * @module material.components.menuBar
 * @restrict E
 * @description
 *
 * Menu bars are containers that hold multiple menus. They change the behavior and appearence
 * of the `md-menu` directive to behave similar to an operating system provided menu.
 *
 * @usage
 * <hljs lang="html">
 * <md-menu-bar>
 *   <md-menu>
 *     <button ng-click="$mdMenu.open()">
 *       File
 *     </button>
 *     <md-menu-content>
 *       <md-menu-item>
 *         <md-button ng-click="ctrl.sampleAction('share', $event)">
 *           Share...
 *         </md-button>
 *       </md-menu-item>
 *       <md-menu-divider></md-menu-divider>
 *       <md-menu-item>
 *       <md-menu-item>
 *         <md-menu>
 *           <md-button ng-click="$mdMenu.open()">New</md-button>
 *           <md-menu-content>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Document', $event)">Document</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Spreadsheet', $event)">Spreadsheet</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Presentation', $event)">Presentation</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Form', $event)">Form</md-button></md-menu-item>
 *             <md-menu-item><md-button ng-click="ctrl.sampleAction('New Drawing', $event)">Drawing</md-button></md-menu-item>
 *           </md-menu-content>
 *         </md-menu>
 *       </md-menu-item>
 *     </md-menu-content>
 *   </md-menu>
 * </md-menu-bar>
 * </hljs>
 *
 * ## Menu Bar Controls
 *
 * You may place `md-menu-items` that function as controls within menu bars.
 * There are two modes that are exposed via the `type` attribute of the `md-menu-item`.
 * `type="checkbox"` will function as a boolean control for the `ng-model` attribute of the
 * `md-menu-item`. `type="radio"` will function like a radio button, setting the `ngModel`
 * to the `string` value of the `value` attribute. If you need non-string values, you can use
 * `ng-value` to provide an expression (this is similar to how angular's native `input[type=radio]` works.
 *
 * <hljs lang="html">
 * <md-menu-bar>
 *  <md-menu>
 *    <button ng-click="$mdMenu.open()">
 *      Sample Menu
 *    </button>
 *    <md-menu-content>
 *      <md-menu-item type="checkbox" ng-model="settings.allowChanges">Allow changes</md-menu-item>
 *      <md-menu-divider></md-menu-divider>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="1">Mode 1</md-menu-item>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="1">Mode 2</md-menu-item>
 *      <md-menu-item type="radio" ng-model="settings.mode" ng-value="1">Mode 3</md-menu-item>
 *    </md-menu-content>
 *  </md-menu>
 * </md-menu-bar>
 * </hljs>
 *
 *
 * ### Nesting Menus
 *
 * Menus may be nested within menu bars. This is commonly called cascading menus.
 * To nest a menu place the nested menu inside the content of the `md-menu-item`.
 * <hljs lang="html">
 * <md-menu-item>
 *   <md-menu>
 *     <button ng-click="$mdMenu.open()">New</md-button>
 *     <md-menu-content>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Document', $event)">Document</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Spreadsheet', $event)">Spreadsheet</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Presentation', $event)">Presentation</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Form', $event)">Form</md-button></md-menu-item>
 *       <md-menu-item><md-button ng-click="ctrl.sampleAction('New Drawing', $event)">Drawing</md-button></md-menu-item>
 *     </md-menu-content>
 *   </md-menu>
 * </md-menu-item>
 * </hljs>
 *
 */

MenuBarDirective.$inject = ["$mdUtil", "$mdTheming"];
angular
  .module('material.components.menuBar')
  .directive('mdMenuBar', MenuBarDirective);

/* @ngInject */
function MenuBarDirective($mdUtil, $mdTheming) {
  return {
    restrict: 'E',
    require: 'mdMenuBar',
    controller: 'MenuBarController',

    compile: function compile(templateEl, templateAttrs) {
      if (!templateAttrs.ariaRole) {
        templateEl[0].setAttribute('role', 'menubar');
      }
      angular.forEach(templateEl[0].children, function(menuEl) {
        if (menuEl.nodeName == 'MD-MENU') {
          if (!menuEl.hasAttribute('md-position-mode')) {
            menuEl.setAttribute('md-position-mode', 'left bottom');

            // Since we're in the compile function and actual `md-buttons` are not compiled yet,
            // we need to query for possible `md-buttons` as well.
            menuEl.querySelector('button, a, md-button').setAttribute('role', 'menuitem');
          }
          var contentEls = $mdUtil.nodesToArray(menuEl.querySelectorAll('md-menu-content'));
          angular.forEach(contentEls, function(contentEl) {
            contentEl.classList.add('md-menu-bar-menu');
            contentEl.classList.add('md-dense');
            if (!contentEl.hasAttribute('width')) {
              contentEl.setAttribute('width', 5);
            }
          });
        }
      });

      // Mark the child menu items that they're inside a menu bar. This is necessary,
      // because mnMenuItem has special behaviour during compilation, depending on
      // whether it is inside a mdMenuBar. We can usually figure this out via the DOM,
      // however if a directive that uses documentFragment is applied to the child (e.g. ngRepeat),
      // the element won't have a parent and won't compile properly.
      templateEl.find('md-menu-item').addClass('md-in-menu-bar');

      return function postLink(scope, el, attr, ctrl) {
        el.addClass('_md');     // private md component indicator for styling
        $mdTheming(scope, el);
        ctrl.init();
      };
    }
  };

}

})();
(function(){
"use strict";


angular
  .module('material.components.menuBar')
  .directive('mdMenuDivider', MenuDividerDirective);


function MenuDividerDirective() {
  return {
    restrict: 'E',
    compile: function(templateEl, templateAttrs) {
      if (!templateAttrs.role) {
        templateEl[0].setAttribute('role', 'separator');
      }
    }
  };
}

})();
(function(){
"use strict";


MenuItemController.$inject = ["$scope", "$element", "$attrs"];
angular
  .module('material.components.menuBar')
  .controller('MenuItemController', MenuItemController);


/**
 * @ngInject
 */
function MenuItemController($scope, $element, $attrs) {
  this.$element = $element;
  this.$attrs = $attrs;
  this.$scope = $scope;
}

MenuItemController.prototype.init = function(ngModel) {
  var $element = this.$element;
  var $attrs = this.$attrs;

  this.ngModel = ngModel;
  if ($attrs.type == 'checkbox' || $attrs.type == 'radio') {
    this.mode  = $attrs.type;
    this.iconEl = $element[0].children[0];
    this.buttonEl = $element[0].children[1];
    if (ngModel) {
      // Clear ngAria set attributes
      this.initClickListeners();
    }
  }
};

// ngAria auto sets attributes on a menu-item with a ngModel.
// We don't want this because our content (buttons) get the focus
// and set their own aria attributes appropritately. Having both
// breaks NVDA / JAWS. This undeoes ngAria's attrs.
MenuItemController.prototype.clearNgAria = function() {
  var el = this.$element[0];
  var clearAttrs = ['role', 'tabindex', 'aria-invalid', 'aria-checked'];
  angular.forEach(clearAttrs, function(attr) {
    el.removeAttribute(attr);
  });
};

MenuItemController.prototype.initClickListeners = function() {
  var self = this;
  var ngModel = this.ngModel;
  var $scope = this.$scope;
  var $attrs = this.$attrs;
  var $element = this.$element;
  var mode = this.mode;

  this.handleClick = angular.bind(this, this.handleClick);

  var icon = this.iconEl;
  var button = angular.element(this.buttonEl);
  var handleClick = this.handleClick;

  $attrs.$observe('disabled', setDisabled);
  setDisabled($attrs.disabled);

  ngModel.$render = function render() {
    self.clearNgAria();
    if (isSelected()) {
      icon.style.display = '';
      button.attr('aria-checked', 'true');
    } else {
      icon.style.display = 'none';
      button.attr('aria-checked', 'false');
    }
  };

  $scope.$$postDigest(ngModel.$render);

  function isSelected() {
    if (mode == 'radio') {
      var val = $attrs.ngValue ? $scope.$eval($attrs.ngValue) : $attrs.value;
      return ngModel.$modelValue == val;
    } else {
      return ngModel.$modelValue;
    }
  }

  function setDisabled(disabled) {
    if (disabled) {
      button.off('click', handleClick);
    } else {
      button.on('click', handleClick);
    }
  }
};

MenuItemController.prototype.handleClick = function(e) {
  var mode = this.mode;
  var ngModel = this.ngModel;
  var $attrs = this.$attrs;
  var newVal;
  if (mode == 'checkbox') {
    newVal = !ngModel.$modelValue;
  } else if (mode == 'radio') {
    newVal = $attrs.ngValue ? this.$scope.$eval($attrs.ngValue) : $attrs.value;
  }
  ngModel.$setViewValue(newVal);
  ngModel.$render();
};

})();
(function(){
"use strict";


MenuItemDirective.$inject = ["$mdUtil", "$mdConstant", "$$mdSvgRegistry"];
angular
  .module('material.components.menuBar')
  .directive('mdMenuItem', MenuItemDirective);

 /* @ngInject */
function MenuItemDirective($mdUtil, $mdConstant, $$mdSvgRegistry) {
  return {
    controller: 'MenuItemController',
    require: ['mdMenuItem', '?ngModel'],
    priority: $mdConstant.BEFORE_NG_ARIA,
    compile: function(templateEl, templateAttrs) {
      var type = templateAttrs.type;
      var inMenuBarClass = 'md-in-menu-bar';

      // Note: This allows us to show the `check` icon for the md-menu-bar items.
      // The `md-in-menu-bar` class is set by the mdMenuBar directive.
      if ((type == 'checkbox' || type == 'radio') && templateEl.hasClass(inMenuBarClass)) {
        var text = templateEl[0].textContent;
        var buttonEl = angular.element('<md-button type="button"></md-button>');
        var iconTemplate = '<md-icon md-svg-src="' + $$mdSvgRegistry.mdChecked + '"></md-icon>';

        buttonEl.html(text);
        buttonEl.attr('tabindex', '0');

        templateEl.html('');
        templateEl.append(angular.element(iconTemplate));
        templateEl.append(buttonEl);
        templateEl.addClass('md-indent').removeClass(inMenuBarClass);

        setDefault('role', type == 'checkbox' ? 'menuitemcheckbox' : 'menuitemradio', buttonEl);
        moveAttrToButton('ng-disabled');

      } else {
        setDefault('role', 'menuitem', templateEl[0].querySelector('md-button, button, a'));
      }


      return function(scope, el, attrs, ctrls) {
        var ctrl = ctrls[0];
        var ngModel = ctrls[1];
        ctrl.init(ngModel);
      };

      function setDefault(attr, val, el) {
        el = el || templateEl;
        if (el instanceof angular.element) {
          el = el[0];
        }
        if (!el.hasAttribute(attr)) {
          el.setAttribute(attr, val);
        }
      }

      function moveAttrToButton(attribute) {
        var attributes = $mdUtil.prefixer(attribute);

        angular.forEach(attributes, function(attr) {
          if (templateEl[0].hasAttribute(attr)) {
            var val = templateEl[0].getAttribute(attr);
            buttonEl[0].setAttribute(attr, val);
            templateEl[0].removeAttribute(attr);
          }
        });
      }
    }
  };
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdProgressCircular
 * @module material.components.progressCircular
 * @restrict E
 *
 * @description
 * The circular progress directive is used to make loading content in your app as delightful and
 * painless as possible by minimizing the amount of visual change a user sees before they can view
 * and interact with content.
 *
 * For operations where the percentage of the operation completed can be determined, use a
 * determinate indicator. They give users a quick sense of how long an operation will take.
 *
 * For operations where the user is asked to wait a moment while something finishes up, and it’s
 * not necessary to expose what's happening behind the scenes and how long it will take, use an
 * indeterminate indicator.
 *
 * @param {string} md-mode Select from one of two modes: **'determinate'** and **'indeterminate'**.
 *
 * Note: if the `md-mode` value is set as undefined or specified as not 1 of the two (2) valid modes, then **'indeterminate'**
 * will be auto-applied as the mode.
 *
 * Note: if not configured, the `md-mode="indeterminate"` will be auto injected as an attribute.
 * If `value=""` is also specified, however, then `md-mode="determinate"` would be auto-injected instead.
 * @param {number=} value In determinate mode, this number represents the percentage of the
 *     circular progress. Default: 0
 * @param {number=} md-diameter This specifies the diameter of the circular progress. The value
 * should be a pixel-size value (eg '100'). If this attribute is
 * not present then a default value of '50px' is assumed.
 *
 * @param {boolean=} ng-disabled Determines whether to disable the progress element.
 *
 * @usage
 * <hljs lang="html">
 * <md-progress-circular md-mode="determinate" value="..."></md-progress-circular>
 *
 * <md-progress-circular md-mode="determinate" ng-value="..."></md-progress-circular>
 *
 * <md-progress-circular md-mode="determinate" value="..." md-diameter="100"></md-progress-circular>
 *
 * <md-progress-circular md-mode="indeterminate"></md-progress-circular>
 * </hljs>
 */

MdProgressCircularDirective.$inject = ["$window", "$mdProgressCircular", "$mdTheming", "$mdUtil", "$interval", "$log"];
angular
  .module('material.components.progressCircular')
  .directive('mdProgressCircular', MdProgressCircularDirective);

/* @ngInject */
function MdProgressCircularDirective($window, $mdProgressCircular, $mdTheming,
                                     $mdUtil, $interval, $log) {

  // Note that this shouldn't use use $$rAF, because it can cause an infinite loop
  // in any tests that call $animate.flush.
  var rAF = $window.requestAnimationFrame ||
            $window.webkitRequestAnimationFrame ||
            angular.noop;

  var cAF = $window.cancelAnimationFrame ||
            $window.webkitCancelAnimationFrame ||
            $window.webkitCancelRequestAnimationFrame ||
            angular.noop;

  var MODE_DETERMINATE = 'determinate';
  var MODE_INDETERMINATE = 'indeterminate';
  var DISABLED_CLASS = '_md-progress-circular-disabled';
  var INDETERMINATE_CLASS = 'md-mode-indeterminate';

  return {
    restrict: 'E',
    scope: {
      value: '@',
      mdDiameter: '@',
      mdMode: '@'
    },
    template:
      '<svg xmlns="http://www.w3.org/2000/svg">' +
        '<path fill="none"/>' +
      '</svg>',
    compile: function(element, attrs) {
      element.attr({
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'role': 'progressbar'
      });

      if (angular.isUndefined(attrs.mdMode)) {
        var mode = attrs.hasOwnProperty('value') ? MODE_DETERMINATE : MODE_INDETERMINATE;
        attrs.$set('mdMode', mode);
      } else {
        attrs.$set('mdMode', attrs.mdMode.trim());
      }

      return MdProgressCircularLink;
    }
  };

  function MdProgressCircularLink(scope, element, attrs) {
    var node = element[0];
    var svg = angular.element(node.querySelector('svg'));
    var path = angular.element(node.querySelector('path'));
    var startIndeterminate = $mdProgressCircular.startIndeterminate;
    var endIndeterminate = $mdProgressCircular.endIndeterminate;
    var iterationCount = 0;
    var lastAnimationId = 0;
    var lastDrawFrame;
    var interval;

    $mdTheming(element);
    element.toggleClass(DISABLED_CLASS, attrs.hasOwnProperty('disabled'));

    // If the mode is indeterminate, it doesn't need to
    // wait for the next digest. It can start right away.
    if(scope.mdMode === MODE_INDETERMINATE){
      startIndeterminateAnimation();
    }

    scope.$on('$destroy', function(){
      cleanupIndeterminateAnimation();

      if (lastDrawFrame) {
        cAF(lastDrawFrame);
      }
    });

    scope.$watchGroup(['value', 'mdMode', function() {
      var isDisabled = node.disabled;

      // Sometimes the browser doesn't return a boolean, in
      // which case we should check whether the attribute is
      // present.
      if (isDisabled === true || isDisabled === false){
        return isDisabled;
      }

      return angular.isDefined(element.attr('disabled'));
    }], function(newValues, oldValues) {
      var mode = newValues[1];
      var isDisabled = newValues[2];
      var wasDisabled = oldValues[2];

      if (isDisabled !== wasDisabled) {
        element.toggleClass(DISABLED_CLASS, !!isDisabled);
      }

      if (isDisabled) {
        cleanupIndeterminateAnimation();
      } else {
        if (mode !== MODE_DETERMINATE && mode !== MODE_INDETERMINATE) {
          mode = MODE_INDETERMINATE;
          attrs.$set('mdMode', mode);
        }

        if (mode === MODE_INDETERMINATE) {
          startIndeterminateAnimation();
        } else {
          var newValue = clamp(newValues[0]);

          cleanupIndeterminateAnimation();

          element.attr('aria-valuenow', newValue);
          renderCircle(clamp(oldValues[0]), newValue);
        }
      }

    });

    // This is in a separate watch in order to avoid layout, unless
    // the value has actually changed.
    scope.$watch('mdDiameter', function(newValue) {
      var diameter = getSize(newValue);
      var strokeWidth = getStroke(diameter);
      var value = clamp(scope.value);
      var transformOrigin = (diameter / 2) + 'px';
      var dimensions = {
        width: diameter + 'px',
        height: diameter + 'px'
      };

      // The viewBox has to be applied via setAttribute, because it is
      // case-sensitive. If jQuery is included in the page, `.attr` lowercases
      // all attribute names.
      svg[0].setAttribute('viewBox', '0 0 ' + diameter + ' ' + diameter);

      // Usually viewBox sets the dimensions for the SVG, however that doesn't
      // seem to be the case on IE10.
      // Important! The transform origin has to be set from here and it has to
      // be in the format of "Ypx Ypx Ypx", otherwise the rotation wobbles in
      // IE and Edge, because they don't account for the stroke width when
      // rotating. Also "center" doesn't help in this case, it has to be a
      // precise value.
      svg
        .css(dimensions)
        .css('transform-origin', transformOrigin + ' ' + transformOrigin + ' ' + transformOrigin);

      element.css(dimensions);

      path.attr('stroke-width', strokeWidth);
      path.attr('stroke-linecap', 'square');
      if (scope.mdMode == MODE_INDETERMINATE) {
        path.attr('d', getSvgArc(diameter, strokeWidth, true));
        path.attr('stroke-dasharray', (diameter - strokeWidth) * $window.Math.PI * 0.75);
        path.attr('stroke-dashoffset', getDashLength(diameter, strokeWidth, 1, 75));
      } else {
        path.attr('d', getSvgArc(diameter, strokeWidth, false));
        path.attr('stroke-dasharray', (diameter - strokeWidth) * $window.Math.PI);
        path.attr('stroke-dashoffset', getDashLength(diameter, strokeWidth, 0, 100));
        renderCircle(value, value);
      }

    });

    function renderCircle(animateFrom, animateTo, easing, duration, iterationCount, maxValue) {
      var id = ++lastAnimationId;
      var startTime = $mdUtil.now();
      var changeInValue = animateTo - animateFrom;
      var diameter = getSize(scope.mdDiameter);
      var strokeWidth = getStroke(diameter);
      var ease = easing || $mdProgressCircular.easeFn;
      var animationDuration = duration || $mdProgressCircular.duration;
      var rotation = -90 * (iterationCount || 0);
      var dashLimit = maxValue || 100;

      // No need to animate it if the values are the same
      if (animateTo === animateFrom) {
        renderFrame(animateTo);
      } else {
        lastDrawFrame = rAF(function animation() {
          var currentTime = $window.Math.max(0, $window.Math.min($mdUtil.now() - startTime, animationDuration));

          renderFrame(ease(currentTime, animateFrom, changeInValue, animationDuration));

          // Do not allow overlapping animations
          if (id === lastAnimationId && currentTime < animationDuration) {
            lastDrawFrame = rAF(animation);
          }
        });
      }

      function renderFrame(value) {
        path.attr('stroke-dashoffset', getDashLength(diameter, strokeWidth, value, dashLimit));
        path.attr('transform','rotate(' + (rotation) + ' ' + diameter/2 + ' ' + diameter/2 + ')');
      }
    }

    function animateIndeterminate() {
      renderCircle(
        startIndeterminate,
        endIndeterminate,
        $mdProgressCircular.easeFnIndeterminate,
        $mdProgressCircular.durationIndeterminate,
        iterationCount,
        75
      );

      // The %4 technically isn't necessary, but it keeps the rotation
      // under 360, instead of becoming a crazy large number.
      iterationCount = ++iterationCount % 4;

    }

    function startIndeterminateAnimation() {
      if (!interval) {
        // Note that this interval isn't supposed to trigger a digest.
        interval = $interval(
          animateIndeterminate,
          $mdProgressCircular.durationIndeterminate,
          0,
          false
        );

        animateIndeterminate();

        element
          .addClass(INDETERMINATE_CLASS)
          .removeAttr('aria-valuenow');
      }
    }

    function cleanupIndeterminateAnimation() {
      if (interval) {
        $interval.cancel(interval);
        interval = null;
        element.removeClass(INDETERMINATE_CLASS);
      }
    }
  }

  /**
   * Returns SVG path data for progress circle
   * Syntax spec: https://www.w3.org/TR/SVG/paths.html#PathDataEllipticalArcCommands
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   * @param {boolean} indeterminate Use if progress circle will be used for indeterminate
   *
   * @returns {string} String representation of an SVG arc.
   */
  function getSvgArc(diameter, strokeWidth, indeterminate) {
    var radius = diameter / 2;
    var offset = strokeWidth / 2;
    var start = radius + ',' + offset; // ie: (25, 2.5) or 12 o'clock
    var end = offset + ',' + radius;   // ie: (2.5, 25) or  9 o'clock
    var arcRadius = radius - offset;
    return 'M' + start
         + 'A' + arcRadius + ',' + arcRadius + ' 0 1 1 ' + end // 75% circle
         + (indeterminate ? '' : 'A' + arcRadius + ',' + arcRadius + ' 0 0 1 ' + start); // loop to start
  }

  /**
   * Return stroke length for progress circle
   *
   * @param {number} diameter Diameter of the container.
   * @param {number} strokeWidth Stroke width to be used when drawing circle
   * @param {number} value Percentage of circle (between 0 and 100)
   * @param {number} limit Max percentage for circle
   *
   * @returns {number} Stroke length for progres circle
   */
  function getDashLength(diameter, strokeWidth, value, limit) {
    return (diameter - strokeWidth) * $window.Math.PI * ( (3 * (limit || 100) / 100) - (value/100) );
  }

  /**
   * Limits a value between 0 and 100.
   */
  function clamp(value) {
    return $window.Math.max(0, $window.Math.min(value || 0, 100));
  }

  /**
   * Determines the size of a progress circle, based on the provided
   * value in the following formats: `X`, `Ypx`, `Z%`.
   */
  function getSize(value) {
    var defaultValue = $mdProgressCircular.progressSize;

    if (value) {
      var parsed = parseFloat(value);

      if (value.lastIndexOf('%') === value.length - 1) {
        parsed = (parsed / 100) * defaultValue;
      }

      return parsed;
    }

    return defaultValue;
  }

  /**
   * Determines the circle's stroke width, based on
   * the provided diameter.
   */
  function getStroke(diameter) {
    return $mdProgressCircular.strokeWidth / 100 * diameter;
  }

}

})();
(function(){
"use strict";

/**
 * @ngdoc service
 * @name $mdProgressCircular
 * @module material.components.progressCircular
 *
 * @description
 * Allows the user to specify the default options for the `progressCircular` directive.
 *
 * @property {number} progressSize Diameter of the progress circle in pixels.
 * @property {number} strokeWidth Width of the circle's stroke as a percentage of the circle's size.
 * @property {number} duration Length of the circle animation in milliseconds.
 * @property {function} easeFn Default easing animation function.
 * @property {object} easingPresets Collection of pre-defined easing functions.
 *
 * @property {number} durationIndeterminate Duration of the indeterminate animation.
 * @property {number} startIndeterminate Indeterminate animation start point.
 * @property {number} endIndeterminate Indeterminate animation end point.
 * @property {function} easeFnIndeterminate Easing function to be used when animating
 * between the indeterminate values.
 *
 * @property {(function(object): object)} configure Used to modify the default options.
 *
 * @usage
 * <hljs lang="js">
 *   myAppModule.config(function($mdProgressCircularProvider) {
 *
 *     // Example of changing the default progress options.
 *     $mdProgressCircularProvider.configure({
 *       progressSize: 100,
 *       strokeWidth: 20,
 *       duration: 800
 *     });
 * });
 * </hljs>
 *
 */

angular
  .module('material.components.progressCircular')
  .provider("$mdProgressCircular", MdProgressCircularProvider);

function MdProgressCircularProvider() {
  var progressConfig = {
    progressSize: 50,
    strokeWidth: 10,
    duration: 100,
    easeFn: linearEase,

    durationIndeterminate: 1333,
    startIndeterminate: 1,
    endIndeterminate: 149,
    easeFnIndeterminate: materialEase,

    easingPresets: {
      linearEase: linearEase,
      materialEase: materialEase
    }
  };

  return {
    configure: function(options) {
      progressConfig = angular.extend(progressConfig, options || {});
      return progressConfig;
    },
    $get: function() { return progressConfig; }
  };

  function linearEase(t, b, c, d) {
    return c * t / d + b;
  }

  function materialEase(t, b, c, d) {
    // via http://www.timotheegroleau.com/Flash/experiments/easing_function_generator.htm
    // with settings of [0, 0, 1, 1]
    var ts = (t /= d) * t;
    var tc = ts * t;
    return b + c * (6 * tc * ts + -15 * ts * ts + 10 * tc);
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdTab
 * @module material.components.tabs
 *
 * @restrict E
 *
 * @description
 * The `<md-tab>` is a nested directive used within `<md-tabs>` to specify a tab with a **label** and optional *view content*.
 *
 * If the `label` attribute is not specified, then an optional `<md-tab-label>` tag can be used to specify more
 * complex tab header markup. If neither the **label** nor the **md-tab-label** are specified, then the nested
 * markup of the `<md-tab>` is used as the tab header markup.
 *
 * Please note that if you use `<md-tab-label>`, your content **MUST** be wrapped in the `<md-tab-body>` tag.  This
 * is to define a clear separation between the tab content and the tab label.
 *
 * This container is used by the TabsController to show/hide the active tab's content view. This synchronization is
 * automatically managed by the internal TabsController whenever the tab selection changes. Selection changes can
 * be initiated via data binding changes, programmatic invocation, or user gestures.
 *
 * @param {string=} label Optional attribute to specify a simple string as the tab label
 * @param {boolean=} ng-disabled If present and expression evaluates to truthy, disabled tab selection.
 * @param {expression=} md-on-deselect Expression to be evaluated after the tab has been de-selected.
 * @param {expression=} md-on-select Expression to be evaluated after the tab has been selected.
 * @param {boolean=} md-active When true, sets the active tab.  Note: There can only be one active tab at a time.
 *
 *
 * @usage
 *
 * <hljs lang="html">
 * <md-tab label="" ng-disabled md-on-select="" md-on-deselect="" >
 *   <h3>My Tab content</h3>
 * </md-tab>
 *
 * <md-tab >
 *   <md-tab-label>
 *     <h3>My Tab content</h3>
 *   </md-tab-label>
 *   <md-tab-body>
 *     <p>
 *       Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium,
 *       totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae
 *       dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit,
 *       sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt.
 *     </p>
 *   </md-tab-body>
 * </md-tab>
 * </hljs>
 *
 */
angular
    .module('material.components.tabs')
    .directive('mdTab', MdTab);

function MdTab () {
  return {
    require:  '^?mdTabs',
    terminal: true,
    compile:  function (element, attr) {
      var label = firstChild(element, 'md-tab-label'),
          body  = firstChild(element, 'md-tab-body');

      if (label.length === 0) {
        label = angular.element('<md-tab-label></md-tab-label>');
        if (attr.label) label.text(attr.label);
        else label.append(element.contents());

        if (body.length === 0) {
          var contents = element.contents().detach();
          body         = angular.element('<md-tab-body></md-tab-body>');
          body.append(contents);
        }
      }

      element.append(label);
      if (body.html()) element.append(body);

      return postLink;
    },
    scope:    {
      active:   '=?mdActive',
      disabled: '=?ngDisabled',
      select:   '&?mdOnSelect',
      deselect: '&?mdOnDeselect'
    }
  };

  function postLink (scope, element, attr, ctrl) {
    if (!ctrl) return;
    var index = ctrl.getTabElementIndex(element),
        body  = firstChild(element, 'md-tab-body').remove(),
        label = firstChild(element, 'md-tab-label').remove(),
        data  = ctrl.insertTab({
          scope:    scope,
          parent:   scope.$parent,
          index:    index,
          element:  element,
          template: body.html(),
          label:    label.html()
        }, index);

    scope.select   = scope.select || angular.noop;
    scope.deselect = scope.deselect || angular.noop;

    scope.$watch('active', function (active) { if (active) ctrl.select(data.getIndex(), true); });
    scope.$watch('disabled', function () { ctrl.refreshIndex(); });
    scope.$watch(
        function () {
          return ctrl.getTabElementIndex(element);
        },
        function (newIndex) {
          data.index = newIndex;
          ctrl.updateTabOrder();
        }
    );
    scope.$on('$destroy', function () { ctrl.removeTab(data); });
  }

  function firstChild (element, tagName) {
    var children = element[0].children;
    for (var i = 0, len = children.length; i < len; i++) {
      var child = children[i];
      if (child.tagName === tagName.toUpperCase()) return angular.element(child);
    }
    return angular.element();
  }
}

})();
(function(){
"use strict";

angular
    .module('material.components.tabs')
    .directive('mdTabItem', MdTabItem);

function MdTabItem () {
  return {
    require: '^?mdTabs',
    link:    function link (scope, element, attr, ctrl) {
      if (!ctrl) return;
      ctrl.attachRipple(scope, element);
    }
  };
}

})();
(function(){
"use strict";

angular
    .module('material.components.tabs')
    .directive('mdTabLabel', MdTabLabel);

function MdTabLabel () {
  return { terminal: true };
}


})();
(function(){
"use strict";


MdTabScroll.$inject = ["$parse"];angular.module('material.components.tabs')
    .directive('mdTabScroll', MdTabScroll);

function MdTabScroll ($parse) {
  return {
    restrict: 'A',
    compile: function ($element, attr) {
      var fn = $parse(attr.mdTabScroll, null, true);
      return function ngEventHandler (scope, element) {
        element.on('mousewheel', function (event) {
          scope.$apply(function () { fn(scope, { $event: event }); });
        });
      };
    }
  };
}

})();
(function(){
"use strict";


MdTabsController.$inject = ["$scope", "$element", "$window", "$mdConstant", "$mdTabInkRipple", "$mdUtil", "$animateCss", "$attrs", "$compile", "$mdTheming", "$mdInteraction", "MdTabsPaginationService"];angular
    .module('material.components.tabs')
    .controller('MdTabsController', MdTabsController);

/**
 * @ngInject
 */
function MdTabsController ($scope, $element, $window, $mdConstant, $mdTabInkRipple, $mdUtil,
                           $animateCss, $attrs, $compile, $mdTheming, $mdInteraction,
                           MdTabsPaginationService) {
  // define private properties
  var ctrl      = this,
      locked    = false,
      elements  = getElements(),
      queue     = [],
      destroyed = false,
      loaded    = false;


  // Define public methods
  ctrl.$onInit            = $onInit;
  ctrl.updatePagination   = $mdUtil.debounce(updatePagination, 100);
  ctrl.redirectFocus      = redirectFocus;
  ctrl.attachRipple       = attachRipple;
  ctrl.insertTab          = insertTab;
  ctrl.removeTab          = removeTab;
  ctrl.select             = select;
  ctrl.scroll             = scroll;
  ctrl.nextPage           = nextPage;
  ctrl.previousPage       = previousPage;
  ctrl.keydown            = keydown;
  ctrl.canPageForward     = canPageForward;
  ctrl.canPageBack        = canPageBack;
  ctrl.refreshIndex       = refreshIndex;
  ctrl.incrementIndex     = incrementIndex;
  ctrl.getTabElementIndex = getTabElementIndex;
  ctrl.updateInkBarStyles = $mdUtil.debounce(updateInkBarStyles, 100);
  ctrl.updateTabOrder     = $mdUtil.debounce(updateTabOrder, 100);
  ctrl.getFocusedTabId    = getFocusedTabId;

  // For AngularJS 1.4 and older, where there are no lifecycle hooks but bindings are pre-assigned,
  // manually call the $onInit hook.
  if (angular.version.major === 1 && angular.version.minor <= 4) {
    this.$onInit();
  }

  /**
   * AngularJS Lifecycle hook for newer AngularJS versions.
   * Bindings are not guaranteed to have been assigned in the controller, but they are in the $onInit hook.
   */
  function $onInit() {
    // Define one-way bindings
    defineOneWayBinding('stretchTabs', handleStretchTabs);

    // Define public properties with change handlers
    defineProperty('focusIndex', handleFocusIndexChange, ctrl.selectedIndex || 0);
    defineProperty('offsetLeft', handleOffsetChange, 0);
    defineProperty('hasContent', handleHasContent, false);
    defineProperty('maxTabWidth', handleMaxTabWidth, getMaxTabWidth());
    defineProperty('shouldPaginate', handleShouldPaginate, false);

    // Define boolean attributes
    defineBooleanAttribute('noInkBar', handleInkBar);
    defineBooleanAttribute('dynamicHeight', handleDynamicHeight);
    defineBooleanAttribute('noPagination');
    defineBooleanAttribute('swipeContent');
    defineBooleanAttribute('noDisconnect');
    defineBooleanAttribute('autoselect');
    defineBooleanAttribute('noSelectClick');
    defineBooleanAttribute('centerTabs', handleCenterTabs, false);
    defineBooleanAttribute('enableDisconnect');

    // Define public properties
    ctrl.scope             = $scope;
    ctrl.parent            = $scope.$parent;
    ctrl.tabs              = [];
    ctrl.lastSelectedIndex = null;
    ctrl.hasFocus          = false;
    ctrl.styleTabItemFocus = false;
    ctrl.shouldCenterTabs  = shouldCenterTabs();
    ctrl.tabContentPrefix  = 'tab-content-';

    // Setup the tabs controller after all bindings are available.
    setupTabsController();
  }

  /**
   * Perform setup for the controller, setup events and watcher(s)
   */
  function setupTabsController () {
    ctrl.selectedIndex = ctrl.selectedIndex || 0;
    compileTemplate();
    configureWatchers();
    bindEvents();
    $mdTheming($element);
    $mdUtil.nextTick(function () {
      // Note that the element references need to be updated, because certain "browsers"
      // (IE/Edge) lose them and start throwing "Invalid calling object" errors, when we
      // compile the element contents down in `compileElement`.
      elements = getElements();
      updateHeightFromContent();
      adjustOffset();
      updateInkBarStyles();
      ctrl.tabs[ ctrl.selectedIndex ] && ctrl.tabs[ ctrl.selectedIndex ].scope.select();
      loaded = true;
      updatePagination();
    });
  }

  /**
   * Compiles the template provided by the user.  This is passed as an attribute from the tabs
   * directive's template function.
   */
  function compileTemplate () {
    var template = $attrs.$mdTabsTemplate,
        element  = angular.element($element[0].querySelector('md-tab-data'));

    element.html(template);
    $compile(element.contents())(ctrl.parent);
    delete $attrs.$mdTabsTemplate;
  }

  /**
   * Binds events used by the tabs component.
   */
  function bindEvents () {
    angular.element($window).on('resize', handleWindowResize);
    $scope.$on('$destroy', cleanup);
  }

  /**
   * Configure watcher(s) used by Tabs
   */
  function configureWatchers () {
    $scope.$watch('$mdTabsCtrl.selectedIndex', handleSelectedIndexChange);
  }

  /**
   * Creates a one-way binding manually rather than relying on AngularJS's isolated scope
   * @param key
   * @param handler
   */
  function defineOneWayBinding (key, handler) {
    var attr = $attrs.$normalize('md-' + key);
    if (handler) defineProperty(key, handler);
    $attrs.$observe(attr, function (newValue) { ctrl[ key ] = newValue; });
  }

  /**
   * Defines boolean attributes with default value set to true.  (ie. md-stretch-tabs with no value
   * will be treated as being truthy)
   * @param key
   * @param handler
   */
  function defineBooleanAttribute (key, handler) {
    var attr = $attrs.$normalize('md-' + key);
    if (handler) defineProperty(key, handler);
    if ($attrs.hasOwnProperty(attr)) updateValue($attrs[attr]);
    $attrs.$observe(attr, updateValue);
    function updateValue (newValue) {
      ctrl[ key ] = newValue !== 'false';
    }
  }

  /**
   * Remove any events defined by this controller
   */
  function cleanup () {
    destroyed = true;
    angular.element($window).off('resize', handleWindowResize);
  }

  // Change handlers

  /**
   * Toggles stretch tabs class and updates inkbar when tab stretching changes
   * @param stretchTabs
   */
  function handleStretchTabs (stretchTabs) {
    var elements = getElements();
    angular.element(elements.wrapper).toggleClass('md-stretch-tabs', shouldStretchTabs());
    updateInkBarStyles();
  }

  function handleCenterTabs (newValue) {
    ctrl.shouldCenterTabs = shouldCenterTabs();
  }

  function handleMaxTabWidth (newWidth, oldWidth) {
    if (newWidth !== oldWidth) {
      var elements = getElements();

      // Set the max width for the real tabs
      angular.forEach(elements.tabs, function(tab) {
        tab.style.maxWidth = newWidth + 'px';
      });

      // Set the max width for the dummy tabs too
      angular.forEach(elements.dummies, function(tab) {
        tab.style.maxWidth = newWidth + 'px';
      });

      $mdUtil.nextTick(ctrl.updateInkBarStyles);
    }
  }

  function handleShouldPaginate (newValue, oldValue) {
    if (newValue !== oldValue) {
      ctrl.maxTabWidth      = getMaxTabWidth();
      ctrl.shouldCenterTabs = shouldCenterTabs();
      $mdUtil.nextTick(function () {
        ctrl.maxTabWidth = getMaxTabWidth();
        adjustOffset(ctrl.selectedIndex);
      });
    }
  }

  /**
   * Add/remove the `md-no-tab-content` class depending on `ctrl.hasContent`
   * @param hasContent
   */
  function handleHasContent (hasContent) {
    $element[ hasContent ? 'removeClass' : 'addClass' ]('md-no-tab-content');
  }

  /**
   * Apply ctrl.offsetLeft to the paging element when it changes
   * @param left
   */
  function handleOffsetChange (left) {
    var elements = getElements();
    var newValue = ((ctrl.shouldCenterTabs || isRtl() ? '' : '-') + left + 'px');

    // Fix double-negative which can happen with RTL support
    newValue = newValue.replace('--', '');

    angular.element(elements.paging).css($mdConstant.CSS.TRANSFORM, 'translate3d(' + newValue + ', 0, 0)');
    $scope.$broadcast('$mdTabsPaginationChanged');
  }

  /**
   * Update the UI whenever `ctrl.focusIndex` is updated
   * @param newIndex
   * @param oldIndex
   */
  function handleFocusIndexChange (newIndex, oldIndex) {
    if (newIndex === oldIndex) return;
    if (!getElements().tabs[ newIndex ]) return;
    adjustOffset();
    redirectFocus();
  }

  /**
   * Update the UI whenever the selected index changes. Calls user-defined select/deselect methods.
   * @param newValue
   * @param oldValue
   */
  function handleSelectedIndexChange (newValue, oldValue) {
    if (newValue === oldValue) return;

    ctrl.selectedIndex     = getNearestSafeIndex(newValue);
    ctrl.lastSelectedIndex = oldValue;
    ctrl.updateInkBarStyles();
    updateHeightFromContent();
    adjustOffset(newValue);
    $scope.$broadcast('$mdTabsChanged');
    ctrl.tabs[ oldValue ] && ctrl.tabs[ oldValue ].scope.deselect();
    ctrl.tabs[ newValue ] && ctrl.tabs[ newValue ].scope.select();
  }

  function getTabElementIndex(tabEl){
    var tabs = $element[0].getElementsByTagName('md-tab');
    return Array.prototype.indexOf.call(tabs, tabEl[0]);
  }

  /**
   * Queues up a call to `handleWindowResize` when a resize occurs while the tabs component is
   * hidden.
   */
  function handleResizeWhenVisible () {
    // if there is already a watcher waiting for resize, do nothing
    if (handleResizeWhenVisible.watcher) return;
    // otherwise, we will abuse the $watch function to check for visible
    handleResizeWhenVisible.watcher = $scope.$watch(function () {
      // since we are checking for DOM size, we use $mdUtil.nextTick() to wait for after the DOM updates
      $mdUtil.nextTick(function () {
        // if the watcher has already run (ie. multiple digests in one cycle), do nothing
        if (!handleResizeWhenVisible.watcher) return;

        if ($element.prop('offsetParent')) {
          handleResizeWhenVisible.watcher();
          handleResizeWhenVisible.watcher = null;

          handleWindowResize();
        }
      }, false);
    });
  }

  // Event handlers / actions

  /**
   * Handle user keyboard interactions
   * @param event
   */
  function keydown (event) {
    switch (event.keyCode) {
      case $mdConstant.KEY_CODE.LEFT_ARROW:
        event.preventDefault();
        incrementIndex(-1, true);
        break;
      case $mdConstant.KEY_CODE.RIGHT_ARROW:
        event.preventDefault();
        incrementIndex(1, true);
        break;
      case $mdConstant.KEY_CODE.SPACE:
      case $mdConstant.KEY_CODE.ENTER:
        event.preventDefault();
        if (!locked) select(ctrl.focusIndex);
        break;
      case $mdConstant.KEY_CODE.TAB:
        // On tabbing out of the tablist, reset hasFocus to reset ng-focused and
        // its md-focused class if the focused tab is not the active tab.
        if (ctrl.focusIndex !== ctrl.selectedIndex) {
          ctrl.focusIndex = ctrl.selectedIndex;
        }
        break;
    }
  }

  /**
   * Update the selected index. Triggers a click event on the original `md-tab` element in order
   * to fire user-added click events if canSkipClick or `md-no-select-click` are false.
   * @param index
   * @param canSkipClick Optionally allow not firing the click event if `md-no-select-click` is also true.
   */
  function select (index, canSkipClick) {
    if (!locked) ctrl.focusIndex = ctrl.selectedIndex = index;
    // skip the click event if noSelectClick is enabled
    if (canSkipClick && ctrl.noSelectClick) return;
    // nextTick is required to prevent errors in user-defined click events
    $mdUtil.nextTick(function () {
      ctrl.tabs[ index ].element.triggerHandler('click');
    }, false);
  }

  /**
   * When pagination is on, this makes sure the selected index is in view.
   * @param event
   */
  function scroll (event) {
    if (!ctrl.shouldPaginate) return;
    event.preventDefault();
    ctrl.offsetLeft = fixOffset(ctrl.offsetLeft - event.wheelDelta);
  }

  /**
   * Slides the tabs over approximately one page forward.
   */
  function nextPage () {
    if (!ctrl.canPageForward()) { return }

    var newOffset = MdTabsPaginationService.increasePageOffset(getElements(), ctrl.offsetLeft);

    ctrl.offsetLeft = fixOffset(newOffset);
  }

  /**
   * Slides the tabs over approximately one page backward.
   */
  function previousPage () {
    if (!ctrl.canPageBack()) { return }

    var newOffset = MdTabsPaginationService.decreasePageOffset(getElements(), ctrl.offsetLeft);

    // Set the new offset
    ctrl.offsetLeft = fixOffset(newOffset);
  }

  /**
   * Update size calculations when the window is resized.
   */
  function handleWindowResize () {
    ctrl.lastSelectedIndex = ctrl.selectedIndex;
    ctrl.offsetLeft        = fixOffset(ctrl.offsetLeft);

    $mdUtil.nextTick(function () {
      ctrl.updateInkBarStyles();
      updatePagination();
    });
  }

  function handleInkBar (hide) {
    angular.element(getElements().inkBar).toggleClass('ng-hide', hide);
  }

  /**
   * Toggle dynamic height class when value changes
   * @param value
   */
  function handleDynamicHeight (value) {
    $element.toggleClass('md-dynamic-height', value);
  }

  /**
   * Remove a tab from the data and select the nearest valid tab.
   * @param tabData
   */
  function removeTab (tabData) {
    if (destroyed) return;
    var selectedIndex = ctrl.selectedIndex,
        tab           = ctrl.tabs.splice(tabData.getIndex(), 1)[ 0 ];
    refreshIndex();
    // when removing a tab, if the selected index did not change, we have to manually trigger the
    //   tab select/deselect events
    if (ctrl.selectedIndex === selectedIndex) {
      tab.scope.deselect();
      ctrl.tabs[ ctrl.selectedIndex ] && ctrl.tabs[ ctrl.selectedIndex ].scope.select();
    }
    $mdUtil.nextTick(function () {
      updatePagination();
      ctrl.offsetLeft = fixOffset(ctrl.offsetLeft);
    });
  }

  /**
   * Create an entry in the tabs array for a new tab at the specified index.
   * @param tabData
   * @param index
   * @returns {*}
   */
  function insertTab (tabData, index) {
    var hasLoaded = loaded;
    var proto     = {
          getIndex:     function () { return ctrl.tabs.indexOf(tab); },
          isActive:     function () { return this.getIndex() === ctrl.selectedIndex; },
          isLeft:       function () { return this.getIndex() < ctrl.selectedIndex; },
          isRight:      function () { return this.getIndex() > ctrl.selectedIndex; },
          shouldRender: function () { return !ctrl.noDisconnect || this.isActive(); },
          hasFocus:     function () {
            return ctrl.styleTabItemFocus
                && ctrl.hasFocus && this.getIndex() === ctrl.focusIndex;
          },
          id:           $mdUtil.nextUid(),
          hasContent: !!(tabData.template && tabData.template.trim())
        },
        tab       = angular.extend(proto, tabData);
    if (angular.isDefined(index)) {
      ctrl.tabs.splice(index, 0, tab);
    } else {
      ctrl.tabs.push(tab);
    }

    processQueue();
    updateHasContent();
    $mdUtil.nextTick(function () {
      updatePagination();
      setAriaControls(tab);

      // if autoselect is enabled, select the newly added tab
      if (hasLoaded && ctrl.autoselect) $mdUtil.nextTick(function () {
        $mdUtil.nextTick(function () { select(ctrl.tabs.indexOf(tab)); });
      });
    });
    return tab;
  }

  // Getter methods

  /**
   * Gathers references to all of the DOM elements used by this controller.
   * @returns {{}}
   */
  function getElements () {
    var elements = {};
    var node = $element[0];

    // gather tab bar elements
    elements.wrapper = node.querySelector('md-tabs-wrapper');
    elements.canvas  = elements.wrapper.querySelector('md-tabs-canvas');
    elements.paging  = elements.canvas.querySelector('md-pagination-wrapper');
    elements.inkBar  = elements.paging.querySelector('md-ink-bar');
    elements.nextButton = node.querySelector('md-next-button');
    elements.prevButton = node.querySelector('md-prev-button');

    elements.contents = node.querySelectorAll('md-tabs-content-wrapper > md-tab-content');
    elements.tabs    = elements.paging.querySelectorAll('md-tab-item');
    elements.dummies = elements.canvas.querySelectorAll('md-dummy-tab');

    return elements;
  }

  /**
   * Determines whether or not the left pagination arrow should be enabled.
   * @returns {boolean}
   */
  function canPageBack () {
    // This works for both LTR and RTL
    return ctrl.offsetLeft > 0;
  }

  /**
   * Determines whether or not the right pagination arrow should be enabled.
   * @returns {*|boolean}
   */
  function canPageForward () {
    var elements = getElements();
    var lastTab = elements.tabs[ elements.tabs.length - 1 ];

    if (isRtl()) {
      return ctrl.offsetLeft < elements.paging.offsetWidth - elements.canvas.offsetWidth;
    }

    return lastTab && lastTab.offsetLeft + lastTab.offsetWidth > elements.canvas.clientWidth +
        ctrl.offsetLeft;
  }

  /**
   * Returns currently focused tab item's element ID
   */
  function getFocusedTabId() {
    var focusedTab = ctrl.tabs[ctrl.focusIndex];
    if (!focusedTab || !focusedTab.id) {
      return null;
    }
    return 'tab-item-' + focusedTab.id;
  }

  /**
   * Determines if the UI should stretch the tabs to fill the available space.
   * @returns {*}
   */
  function shouldStretchTabs () {
    switch (ctrl.stretchTabs) {
      case 'always':
        return true;
      case 'never':
        return false;
      default:
        return !ctrl.shouldPaginate
            && $window.matchMedia('(max-width: 600px)').matches;
    }
  }

  /**
   * Determines if the tabs should appear centered.
   * @returns {string|boolean}
   */
  function shouldCenterTabs () {
    return ctrl.centerTabs && !ctrl.shouldPaginate;
  }

  /**
   * Determines if pagination is necessary to display the tabs within the available space.
   * @returns {boolean}
   */
  function shouldPaginate () {
    if (ctrl.noPagination || !loaded) return false;
    var canvasWidth = $element.prop('clientWidth');

    angular.forEach(getElements().tabs, function (tab) {
      canvasWidth -= tab.offsetWidth;
    });

    return canvasWidth < 0;
  }

  /**
   * Finds the nearest tab index that is available.  This is primarily used for when the active
   * tab is removed.
   * @param newIndex
   * @returns {*}
   */
  function getNearestSafeIndex (newIndex) {
    if (newIndex === -1) return -1;
    var maxOffset = Math.max(ctrl.tabs.length - newIndex, newIndex),
        i, tab;
    for (i = 0; i <= maxOffset; i++) {
      tab = ctrl.tabs[ newIndex + i ];
      if (tab && (tab.scope.disabled !== true)) return tab.getIndex();
      tab = ctrl.tabs[ newIndex - i ];
      if (tab && (tab.scope.disabled !== true)) return tab.getIndex();
    }
    return newIndex;
  }

  // Utility methods

  /**
   * Defines a property using a getter and setter in order to trigger a change handler without
   * using `$watch` to observe changes.
   * @param key
   * @param handler
   * @param value
   */
  function defineProperty (key, handler, value) {
    Object.defineProperty(ctrl, key, {
      get: function () { return value; },
      set: function (newValue) {
        var oldValue = value;
        value        = newValue;
        handler && handler(newValue, oldValue);
      }
    });
  }

  /**
   * Updates whether or not pagination should be displayed.
   */
  function updatePagination () {
    ctrl.maxTabWidth = getMaxTabWidth();
    ctrl.shouldPaginate = shouldPaginate();
  }

  /**
   * Calculates the width of the pagination wrapper by summing the widths of the dummy tabs.
   * @returns {number}
   */
  function calcPagingWidth () {
    return calcTabsWidth(getElements().tabs);
  }

  function calcTabsWidth(tabs) {
    var width = 0;

    angular.forEach(tabs, function (tab) {
      //-- Uses the larger value between `getBoundingClientRect().width` and `offsetWidth`.  This
      //   prevents `offsetWidth` value from being rounded down and causing wrapping issues, but
      //   also handles scenarios where `getBoundingClientRect()` is inaccurate (ie. tabs inside
      //   of a dialog)
      width += Math.max(tab.offsetWidth, tab.getBoundingClientRect().width);
    });

    return Math.ceil(width);
  }

  function getMaxTabWidth () {
    var elements = getElements(),
        containerWidth = elements.canvas.clientWidth,

        // See https://material.google.com/components/tabs.html#tabs-specs
        specMax = 264;

    // Do the spec maximum, or the canvas width; whichever is *smaller* (tabs larger than the canvas
    // width can break the pagination) but not less than 0
    return Math.max(0, Math.min(containerWidth - 1, specMax));
  }

  function getMinTabWidth() {
    var elements = getElements(),
        containerWidth = elements.canvas.clientWidth,
        xsBreakpoint = 600,

        // See https://material.google.com/components/tabs.html#tabs-specs
        specMin = containerWidth > xsBreakpoint ? 160 : 72;

    // Do the spec minimum, or the canvas width; whichever is *smaller* (tabs larger than the canvas
    // width can break the pagination) but not less than 0
    return Math.max(0, Math.min(containerWidth - 1, specMin));
  }

  /**
   * Re-orders the tabs and updates the selected and focus indexes to their new positions.
   * This is triggered by `tabDirective.js` when the user's tabs have been re-ordered.
   */
  function updateTabOrder () {
    var selectedItem   = ctrl.tabs[ ctrl.selectedIndex ],
        focusItem      = ctrl.tabs[ ctrl.focusIndex ];
    ctrl.tabs          = ctrl.tabs.sort(function (a, b) {
      return a.index - b.index;
    });
    ctrl.selectedIndex = ctrl.tabs.indexOf(selectedItem);
    ctrl.focusIndex    = ctrl.tabs.indexOf(focusItem);
  }

  /**
   * This moves the selected or focus index left or right.  This is used by the keydown handler.
   * @param inc
   */
  function incrementIndex (inc, focus) {
    var newIndex,
        key   = focus ? 'focusIndex' : 'selectedIndex',
        index = ctrl[ key ];
    for (newIndex = index + inc;
         ctrl.tabs[ newIndex ] && ctrl.tabs[ newIndex ].scope.disabled;
         newIndex += inc) {}

    newIndex = (index + inc + ctrl.tabs.length) % ctrl.tabs.length;

    if (ctrl.tabs[ newIndex ]) {
      ctrl[ key ] = newIndex;
    }
  }

  /**
   * This is used to forward focus to tab container elements.  This method is necessary to avoid
   * animation issues when attempting to focus an item that is out of view.
   */
  function redirectFocus () {
    ctrl.styleTabItemFocus = ($mdInteraction.getLastInteractionType() === 'keyboard');
    getElements().tabs[ ctrl.focusIndex ].focus();
  }

  /**
   * Forces the pagination to move the focused tab into view.
   */
  function adjustOffset (index) {
    var elements = getElements();

    if (!angular.isNumber(index)) index = ctrl.focusIndex;
    if (!elements.tabs[ index ]) return;
    if (ctrl.shouldCenterTabs) return;
    var tab         = elements.tabs[ index ],
        left        = tab.offsetLeft,
        right       = tab.offsetWidth + left,
        extraOffset = 32;

    // If we are selecting the first tab (in LTR and RTL), always set the offset to 0
    if (index == 0) {
      ctrl.offsetLeft = 0;
      return;
    }

    if (isRtl()) {
      var tabWidthsBefore = calcTabsWidth(Array.prototype.slice.call(elements.tabs, 0, index));
      var tabWidthsIncluding = calcTabsWidth(Array.prototype.slice.call(elements.tabs, 0, index + 1));

      ctrl.offsetLeft = Math.min(ctrl.offsetLeft, fixOffset(tabWidthsBefore));
      ctrl.offsetLeft = Math.max(ctrl.offsetLeft, fixOffset(tabWidthsIncluding - elements.canvas.clientWidth));
    } else {
      ctrl.offsetLeft = Math.max(ctrl.offsetLeft, fixOffset(right - elements.canvas.clientWidth + extraOffset));
      ctrl.offsetLeft = Math.min(ctrl.offsetLeft, fixOffset(left));
    }
  }

  /**
   * Iterates through all queued functions and clears the queue.  This is used for functions that
   * are called before the UI is ready, such as size calculations.
   */
  function processQueue () {
    queue.forEach(function (func) { $mdUtil.nextTick(func); });
    queue = [];
  }

  /**
   * Determines if the tab content area is needed.
   */
  function updateHasContent () {
    var hasContent  = false;

    for (var i = 0; i < ctrl.tabs.length; i++) {
      if (ctrl.tabs[i].hasContent) {
        hasContent = true;
        break;
      }
    }

    ctrl.hasContent = hasContent;
  }

  /**
   * Moves the indexes to their nearest valid values.
   */
  function refreshIndex () {
    ctrl.selectedIndex = getNearestSafeIndex(ctrl.selectedIndex);
    ctrl.focusIndex    = getNearestSafeIndex(ctrl.focusIndex);
  }

  /**
   * Calculates the content height of the current tab.
   * @returns {*}
   */
  function updateHeightFromContent () {
    if (!ctrl.dynamicHeight) return $element.css('height', '');
    if (!ctrl.tabs.length) return queue.push(updateHeightFromContent);

    var elements = getElements();

    var tabContent    = elements.contents[ ctrl.selectedIndex ],
        contentHeight = tabContent ? tabContent.offsetHeight : 0,
        tabsHeight    = elements.wrapper.offsetHeight,
        newHeight     = contentHeight + tabsHeight,
        currentHeight = $element.prop('clientHeight');

    if (currentHeight === newHeight) return;

    // Adjusts calculations for when the buttons are bottom-aligned since this relies on absolute
    // positioning.  This should probably be cleaned up if a cleaner solution is possible.
    if ($element.attr('md-align-tabs') === 'bottom') {
      currentHeight -= tabsHeight;
      newHeight -= tabsHeight;
      // Need to include bottom border in these calculations
      if ($element.attr('md-border-bottom') !== undefined) ++currentHeight;
    }

    // Lock during animation so the user can't change tabs
    locked = true;

    var fromHeight = { height: currentHeight + 'px' },
        toHeight = { height: newHeight + 'px' };

    // Set the height to the current, specific pixel height to fix a bug on iOS where the height
    // first animates to 0, then back to the proper height causing a visual glitch
    $element.css(fromHeight);

    // Animate the height from the old to the new
    $animateCss($element, {
      from: fromHeight,
      to: toHeight,
      easing: 'cubic-bezier(0.35, 0, 0.25, 1)',
      duration: 0.5
    }).start().done(function () {
      // Then (to fix the same iOS issue as above), disable transitions and remove the specific
      // pixel height so the height can size with browser width/content changes, etc.
      $element.css({
        transition: 'none',
        height: ''
      });

      // In the next tick, re-allow transitions (if we do it all at once, $element.css is "smart"
      // enough to batch it for us instead of doing it immediately, which undoes the original
      // transition: none)
      $mdUtil.nextTick(function() {
        $element.css('transition', '');
      });

      // And unlock so tab changes can occur
      locked = false;
    });
  }

  /**
   * Repositions the ink bar to the selected tab.
   * @returns {*}
   */
  function updateInkBarStyles () {
    var elements = getElements();

    if (!elements.tabs[ ctrl.selectedIndex ]) {
      angular.element(elements.inkBar).css({ left: 'auto', right: 'auto' });
      return;
    }

    if (!ctrl.tabs.length) return queue.push(ctrl.updateInkBarStyles);
    // if the element is not visible, we will not be able to calculate sizes until it is
    // we should treat that as a resize event rather than just updating the ink bar
    if (!$element.prop('offsetParent')) return handleResizeWhenVisible();

    var index      = ctrl.selectedIndex,
        totalWidth = elements.paging.offsetWidth,
        tab        = elements.tabs[ index ],
        left       = tab.offsetLeft,
        right      = totalWidth - left - tab.offsetWidth;

    if (ctrl.shouldCenterTabs) {
      // We need to use the same calculate process as in the pagination wrapper, to avoid rounding deviations.
      var tabWidth = calcTabsWidth(elements.tabs);

      if (totalWidth > tabWidth) {
        $mdUtil.nextTick(updateInkBarStyles, false);
      }
    }
    updateInkBarClassName();
    angular.element(elements.inkBar).css({ left: left + 'px', right: right + 'px' });
  }

  /**
   * Adds left/right classes so that the ink bar will animate properly.
   */
  function updateInkBarClassName () {
    var elements = getElements();
    var newIndex = ctrl.selectedIndex,
        oldIndex = ctrl.lastSelectedIndex,
        ink      = angular.element(elements.inkBar);
    if (!angular.isNumber(oldIndex)) return;
    ink
        .toggleClass('md-left', newIndex < oldIndex)
        .toggleClass('md-right', newIndex > oldIndex);
  }

  /**
   * Takes an offset value and makes sure that it is within the min/max allowed values.
   * @param value
   * @returns {*}
   */
  function fixOffset (value) {
    var elements = getElements();

    if (!elements.tabs.length || !ctrl.shouldPaginate) return 0;

    var lastTab    = elements.tabs[ elements.tabs.length - 1 ],
        totalWidth = lastTab.offsetLeft + lastTab.offsetWidth;

    if (isRtl()) {
      value = Math.min(elements.paging.offsetWidth - elements.canvas.clientWidth, value);
      value = Math.max(0, value);
    } else {
      value = Math.max(0, value);
      value = Math.min(totalWidth - elements.canvas.clientWidth, value);
    }

    return value;
  }

  /**
   * Attaches a ripple to the tab item element.
   * @param scope
   * @param element
   */
  function attachRipple (scope, element) {
    var elements = getElements();
    var options = { colorElement: angular.element(elements.inkBar) };
    $mdTabInkRipple.attach(scope, element, options);
  }

  /**
   * Sets the `aria-controls` attribute to the elements that
   * correspond to the passed-in tab.
   * @param tab
   */
  function setAriaControls (tab) {
    if (tab.hasContent) {
      var nodes = $element[0].querySelectorAll('[md-tab-id="' + tab.id + '"]');
      angular.element(nodes).attr('aria-controls', ctrl.tabContentPrefix + tab.id);
    }
  }

  function isRtl() {
    return ($mdUtil.bidi() == 'rtl');
  }
}

})();
(function(){
"use strict";

/**
 * @ngdoc directive
 * @name mdTabs
 * @module material.components.tabs
 *
 * @restrict E
 *
 * @description
 * The `<md-tabs>` directive serves as the container for 1..n `<md-tab>` child directives to
 * produces a Tabs components. In turn, the nested `<md-tab>` directive is used to specify a tab
 * label for the **header button** and a [optional] tab view content that will be associated with
 * each tab button.
 *
 * Below is the markup for its simplest usage:
 *
 *  <hljs lang="html">
 *  <md-tabs>
 *    <md-tab label="Tab #1"></md-tab>
 *    <md-tab label="Tab #2"></md-tab>
 *    <md-tab label="Tab #3"></md-tab>
 *  </md-tabs>
 *  </hljs>
 *
 * Tabs supports three (3) usage scenarios:
 *
 *  1. Tabs (buttons only)
 *  2. Tabs with internal view content
 *  3. Tabs with external view content
 *
 * **Tab-only** support is useful when tab buttons are used for custom navigation regardless of any
 * other components, content, or views.
 *
 * <i><b>Note:</b> If you are using the Tabs component for page-level navigation, please take a look
 * at the <a ng-href="./api/directive/mdNavBar">NavBar component</a> instead as it can handle this
 * case a bit more natively.</i>
 *
 * **Tabs with internal views** are the traditional usages where each tab has associated view
 * content and the view switching is managed internally by the Tabs component.
 *
 * **Tabs with external view content** is often useful when content associated with each tab is
 * independently managed and data-binding notifications announce tab selection changes.
 *
 * Additional features also include:
 *
 * *  Content can include any markup.
 * *  If a tab is disabled while active/selected, then the next tab will be auto-selected.
 *
 * ### Explanation of tab stretching
 *
 * Initially, tabs will have an inherent size.  This size will either be defined by how much space is needed to accommodate their text or set by the user through CSS.  Calculations will be based on this size.
 *
 * On mobile devices, tabs will be expanded to fill the available horizontal space.  When this happens, all tabs will become the same size.
 *
 * On desktops, by default, stretching will never occur.
 *
 * This default behavior can be overridden through the `md-stretch-tabs` attribute.  Here is a table showing when stretching will occur:
 *
 * `md-stretch-tabs` | mobile    | desktop
 * ------------------|-----------|--------
 * `auto`            | stretched | ---
 * `always`          | stretched | stretched
 * `never`           | ---       | ---
 *
 * @param {integer=} md-selected Index of the active/selected tab
 * @param {boolean=} md-no-ink If present, disables ink ripple effects.
 * @param {boolean=} md-no-ink-bar If present, disables the selection ink bar.
 * @param {string=}  md-align-tabs Attribute to indicate position of tab buttons: `bottom` or `top`; default is `top`
 * @param {string=} md-stretch-tabs Attribute to indicate whether or not to stretch tabs: `auto`, `always`, or `never`; default is `auto`
 * @param {boolean=} md-dynamic-height When enabled, the tab wrapper will resize based on the contents of the selected tab
 * @param {boolean=} md-border-bottom If present, shows a solid `1px` border between the tabs and their content
 * @param {boolean=} md-center-tabs When enabled, tabs will be centered provided there is no need for pagination
 * @param {boolean=} md-no-pagination When enabled, pagination will remain off
 * @param {boolean=} md-swipe-content When enabled, swipe gestures will be enabled for the content area to jump between tabs
 * @param {boolean=} md-enable-disconnect When enabled, scopes will be disconnected for tabs that are not being displayed.  This provides a performance boost, but may also cause unexpected issues and is not recommended for most users.
 * @param {boolean=} md-autoselect When present, any tabs added after the initial load will be automatically selected
 * @param {boolean=} md-no-select-click When enabled, click events will not be fired when selecting tabs
 *
 * @usage
 * <hljs lang="html">
 * <md-tabs md-selected="selectedIndex" >
 *   <img ng-src="img/angular.png" class="centered">
 *   <md-tab
 *       ng-repeat="tab in tabs | orderBy:predicate:reversed"
 *       md-on-select="onTabSelected(tab)"
 *       md-on-deselect="announceDeselected(tab)"
 *       ng-disabled="tab.disabled">
 *     <md-tab-label>
 *       {{tab.title}}
 *       <img src="img/removeTab.png" ng-click="removeTab(tab)" class="delete">
 *     </md-tab-label>
 *     <md-tab-body>
 *       {{tab.content}}
 *     </md-tab-body>
 *   </md-tab>
 * </md-tabs>
 * </hljs>
 *
 */
MdTabs.$inject = ["$$mdSvgRegistry"];
angular
    .module('material.components.tabs')
    .directive('mdTabs', MdTabs);

function MdTabs ($$mdSvgRegistry) {
  return {
    scope:            {
      selectedIndex: '=?mdSelected'
    },
    template:         function (element, attr) {
      attr.$mdTabsTemplate = element.html();
      return '' +
        '<md-tabs-wrapper> ' +
          '<md-tab-data></md-tab-data> ' +
          '<md-prev-button ' +
              'tabindex="-1" ' +
              'role="button" ' +
              'aria-label="Previous Page" ' +
              'aria-disabled="{{!$mdTabsCtrl.canPageBack()}}" ' +
              'ng-class="{ \'md-disabled\': !$mdTabsCtrl.canPageBack() }" ' +
              'ng-if="$mdTabsCtrl.shouldPaginate" ' +
              'ng-click="$mdTabsCtrl.previousPage()"> ' +
            '<md-icon md-svg-src="'+ $$mdSvgRegistry.mdTabsArrow +'"></md-icon> ' +
          '</md-prev-button> ' +
          '<md-next-button ' +
              'tabindex="-1" ' +
              'role="button" ' +
              'aria-label="Next Page" ' +
              'aria-disabled="{{!$mdTabsCtrl.canPageForward()}}" ' +
              'ng-class="{ \'md-disabled\': !$mdTabsCtrl.canPageForward() }" ' +
              'ng-if="$mdTabsCtrl.shouldPaginate" ' +
              'ng-click="$mdTabsCtrl.nextPage()"> ' +
            '<md-icon md-svg-src="'+ $$mdSvgRegistry.mdTabsArrow +'"></md-icon> ' +
          '</md-next-button> ' +
          '<md-tabs-canvas ' +
              'ng-focus="$mdTabsCtrl.redirectFocus()" ' +
              'ng-class="{ ' +
                  '\'md-paginated\': $mdTabsCtrl.shouldPaginate, ' +
                  '\'md-center-tabs\': $mdTabsCtrl.shouldCenterTabs ' +
              '}" ' +
              'ng-keydown="$mdTabsCtrl.keydown($event)"> ' +
            '<md-pagination-wrapper ' +
                'ng-class="{ \'md-center-tabs\': $mdTabsCtrl.shouldCenterTabs }" ' +
                'md-tab-scroll="$mdTabsCtrl.scroll($event)" ' +
                'role="tablist"> ' +
              '<md-tab-item ' +
                  'tabindex="{{ tab.isActive() ? 0 : -1 }}" ' +
                  'class="md-tab" ' +
                  'ng-repeat="tab in $mdTabsCtrl.tabs" ' +
                  'role="tab" ' +
                  'id="tab-item-{{::tab.id}}" ' +
                  'md-tab-id="{{::tab.id}}"' +
                  'aria-selected="{{tab.isActive()}}" ' +
                  'aria-disabled="{{tab.scope.disabled || \'false\'}}" ' +
                  'ng-click="$mdTabsCtrl.select(tab.getIndex())" ' +
                  'ng-focus="$mdTabsCtrl.hasFocus = true" ' +
                  'ng-blur="$mdTabsCtrl.hasFocus = false" ' +
                  'ng-class="{ ' +
                      '\'md-active\':    tab.isActive(), ' +
                      '\'md-focused\':   tab.hasFocus(), ' +
                      '\'md-disabled\':  tab.scope.disabled ' +
                  '}" ' +
                  'ng-disabled="tab.scope.disabled" ' +
                  'md-swipe-left="$mdTabsCtrl.nextPage()" ' +
                  'md-swipe-right="$mdTabsCtrl.previousPage()" ' +
                  'md-tabs-template="::tab.label" ' +
                  'md-scope="::tab.parent"></md-tab-item> ' +
              '<md-ink-bar></md-ink-bar> ' +
            '</md-pagination-wrapper> ' +
            '<md-tabs-dummy-wrapper aria-hidden="true" class="md-visually-hidden md-dummy-wrapper"> ' +
              '<md-dummy-tab ' +
                  'class="md-tab" ' +
                  'tabindex="-1" ' +
                  'ng-repeat="tab in $mdTabsCtrl.tabs" ' +
                  'md-tabs-template="::tab.label" ' +
                  'md-scope="::tab.parent"></md-dummy-tab> ' +
            '</md-tabs-dummy-wrapper> ' +
          '</md-tabs-canvas> ' +
        '</md-tabs-wrapper> ' +
        '<md-tabs-content-wrapper ng-show="$mdTabsCtrl.hasContent && $mdTabsCtrl.selectedIndex >= 0" class="_md"> ' +
          '<md-tab-content ' +
              'id="{{:: $mdTabsCtrl.tabContentPrefix + tab.id}}" ' +
              'class="_md" ' +
              'role="tabpanel" ' +
              'aria-labelledby="tab-item-{{::tab.id}}" ' +
              'md-swipe-left="$mdTabsCtrl.swipeContent && $mdTabsCtrl.incrementIndex(1)" ' +
              'md-swipe-right="$mdTabsCtrl.swipeContent && $mdTabsCtrl.incrementIndex(-1)" ' +
              'ng-if="tab.hasContent" ' +
              'ng-repeat="(index, tab) in $mdTabsCtrl.tabs" ' +
              'ng-class="{ ' +
                '\'md-no-transition\': $mdTabsCtrl.lastSelectedIndex == null, ' +
                '\'md-active\':        tab.isActive(), ' +
                '\'md-left\':          tab.isLeft(), ' +
                '\'md-right\':         tab.isRight(), ' +
                '\'md-no-scroll\':     $mdTabsCtrl.dynamicHeight ' +
              '}"> ' +
            '<div ' +
                'md-tabs-template="::tab.template" ' +
                'md-connected-if="tab.isActive()" ' +
                'md-scope="::tab.parent" ' +
                'ng-if="$mdTabsCtrl.enableDisconnect || tab.shouldRender()"></div> ' +
          '</md-tab-content> ' +
        '</md-tabs-content-wrapper>';
    },
    controller:       'MdTabsController',
    controllerAs:     '$mdTabsCtrl',
    bindToController: true
  };
}

})();
(function(){
"use strict";


MdTabsDummyWrapper.$inject = ["$mdUtil", "$window"];angular
  .module('material.components.tabs')
  .directive('mdTabsDummyWrapper', MdTabsDummyWrapper);

/**
 * @private
 *
 * @param $mdUtil
 * @param $window
 * @returns {{require: string, link: link}}
 * @constructor
 *
 * @ngInject
 */
function MdTabsDummyWrapper ($mdUtil, $window) {
  return {
    require: '^?mdTabs',
    link:    function link (scope, element, attr, ctrl) {
      if (!ctrl) return;

      var observer;
      var disconnect;

      var mutationCallback = function() {
        ctrl.updatePagination();
        ctrl.updateInkBarStyles();
      };

      if('MutationObserver' in $window) {
        var config = {
          childList: true,
          subtree: true,
          // Per https://bugzilla.mozilla.org/show_bug.cgi?id=1138368, browsers will not fire
          // the childList mutation, once a <span> element's innerText changes.
          // The characterData of the <span> element will change.
          characterData: true
        };

        observer = new MutationObserver(mutationCallback);
        observer.observe(element[0], config);
        disconnect = observer.disconnect.bind(observer);
      } else {
        var debounced = $mdUtil.debounce(mutationCallback, 15, null, false);
        
        element.on('DOMSubtreeModified', debounced);
        disconnect = element.off.bind(element, 'DOMSubtreeModified', debounced);
      }

      // Disconnect the observer
      scope.$on('$destroy', function() {
        disconnect();
      });
    }
  };
}

})();
(function(){
"use strict";


MdTabsTemplate.$inject = ["$compile", "$mdUtil"];angular
    .module('material.components.tabs')
    .directive('mdTabsTemplate', MdTabsTemplate);

function MdTabsTemplate ($compile, $mdUtil) {
  return {
    restrict: 'A',
    link:     link,
    scope:    {
      template:     '=mdTabsTemplate',
      connected:    '=?mdConnectedIf',
      compileScope: '=mdScope'
    },
    require:  '^?mdTabs'
  };
  function link (scope, element, attr, ctrl) {
    if (!ctrl) return;

    var compileScope = ctrl.enableDisconnect ? scope.compileScope.$new() : scope.compileScope;

    element.html(scope.template);
    $compile(element.contents())(compileScope);

    return $mdUtil.nextTick(handleScope);

    function handleScope () {
      scope.$watch('connected', function (value) { value === false ? disconnect() : reconnect(); });
      scope.$on('$destroy', reconnect);
    }

    function disconnect () {
      if (ctrl.enableDisconnect) $mdUtil.disconnectScope(compileScope);
    }

    function reconnect () {
      if (ctrl.enableDisconnect) $mdUtil.reconnectScope(compileScope);
    }
  }
}

})();
(function(){ 
angular.module("material.core").constant("$MD_THEME_CSS", "md-autocomplete.md-THEME_NAME-theme{background:\"{{background-A100}}\"}md-autocomplete.md-THEME_NAME-theme[disabled]:not([md-floating-label]){background:\"{{background-100}}\"}md-autocomplete.md-THEME_NAME-theme button md-icon path{fill:\"{{background-600}}\"}md-autocomplete.md-THEME_NAME-theme button:after{background:\"{{background-600-0.3}}\"}.md-autocomplete-suggestions-container.md-THEME_NAME-theme{background:\"{{background-A100}}\"}.md-autocomplete-suggestions-container.md-THEME_NAME-theme li{color:\"{{background-900}}\"}.md-autocomplete-suggestions-container.md-THEME_NAME-theme li .highlight{color:\"{{background-600}}\"}.md-autocomplete-suggestions-container.md-THEME_NAME-theme li.selected,.md-autocomplete-suggestions-container.md-THEME_NAME-theme li:hover{background:\"{{background-200}}\"}md-backdrop{background-color:\"{{background-900-0.0}}\"}md-backdrop.md-opaque.md-THEME_NAME-theme{background-color:\"{{background-900-1.0}}\"}md-bottom-sheet.md-THEME_NAME-theme{background-color:\"{{background-50}}\";border-top-color:\"{{background-300}}\"}md-bottom-sheet.md-THEME_NAME-theme.md-list md-list-item{color:\"{{foreground-1}}\"}md-bottom-sheet.md-THEME_NAME-theme .md-subheader{background-color:\"{{background-50}}\";color:\"{{foreground-1}}\"}.md-button.md-THEME_NAME-theme:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme:not([disabled]):hover{background-color:\"{{background-500-0.2}}\"}.md-button.md-THEME_NAME-theme:not([disabled]).md-icon-button:hover{background-color:transparent}.md-button.md-THEME_NAME-theme.md-fab md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab,.md-button.md-THEME_NAME-theme.md-primary.md-raised{color:\"{{primary-contrast}}\";background-color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]) md-icon{color:\"{{primary-contrast}}\"}.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-primary.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-primary.md-raised:not([disabled]):hover{background-color:\"{{primary-600}}\"}.md-button.md-THEME_NAME-theme.md-primary:not([disabled]) md-icon{color:\"{{primary-color}}\"}.md-button.md-THEME_NAME-theme.md-fab{background-color:\"{{accent-color}}\";color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-fab:not([disabled]) .md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-fab:not([disabled]):hover{background-color:\"{{accent-A700}}\"}.md-button.md-THEME_NAME-theme.md-raised{color:\"{{background-900}}\";background-color:\"{{background-50}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]) md-icon{color:\"{{background-900}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]):hover{background-color:\"{{background-50}}\"}.md-button.md-THEME_NAME-theme.md-raised:not([disabled]).md-focused{background-color:\"{{background-200}}\"}.md-button.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab,.md-button.md-THEME_NAME-theme.md-warn.md-raised{color:\"{{warn-contrast}}\";background-color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]) md-icon{color:\"{{warn-contrast}}\"}.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-warn.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-warn.md-raised:not([disabled]):hover{background-color:\"{{warn-600}}\"}.md-button.md-THEME_NAME-theme.md-warn:not([disabled]) md-icon{color:\"{{warn-color}}\"}.md-button.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab,.md-button.md-THEME_NAME-theme.md-accent.md-raised{color:\"{{accent-contrast}}\";background-color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]) md-icon,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]) md-icon{color:\"{{accent-contrast}}\"}.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-accent.md-fab:not([disabled]):hover,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]).md-focused,.md-button.md-THEME_NAME-theme.md-accent.md-raised:not([disabled]):hover{background-color:\"{{accent-A700}}\"}.md-button.md-THEME_NAME-theme.md-accent:not([disabled]) md-icon{color:\"{{accent-color}}\"}.md-button.md-THEME_NAME-theme.md-accent[disabled],.md-button.md-THEME_NAME-theme.md-fab[disabled],.md-button.md-THEME_NAME-theme.md-raised[disabled],.md-button.md-THEME_NAME-theme.md-warn[disabled],.md-button.md-THEME_NAME-theme[disabled]{color:\"{{foreground-3}}\";cursor:default}.md-button.md-THEME_NAME-theme.md-accent[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-fab[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-raised[disabled] md-icon,.md-button.md-THEME_NAME-theme.md-warn[disabled] md-icon,.md-button.md-THEME_NAME-theme[disabled] md-icon{color:\"{{foreground-3}}\"}.md-button.md-THEME_NAME-theme.md-fab[disabled],.md-button.md-THEME_NAME-theme.md-raised[disabled]{background-color:\"{{foreground-4}}\"}.md-button.md-THEME_NAME-theme[disabled]{background-color:transparent}._md a.md-THEME_NAME-theme:not(.md-button).md-primary{color:\"{{primary-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-primary:hover{color:\"{{primary-700}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-accent{color:\"{{accent-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-accent:hover{color:\"{{accent-A700}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-warn{color:\"{{warn-color}}\"}._md a.md-THEME_NAME-theme:not(.md-button).md-warn:hover{color:\"{{warn-700}}\"}md-card.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-hue-1}}\";border-radius:2px}md-card.md-THEME_NAME-theme .md-card-image{border-radius:2px 2px 0 0}md-card.md-THEME_NAME-theme md-card-header md-card-avatar md-icon{color:\"{{background-color}}\";background-color:\"{{foreground-3}}\"}md-card.md-THEME_NAME-theme md-card-header md-card-header-text .md-subhead,md-card.md-THEME_NAME-theme md-card-title md-card-title-text:not(:only-child) .md-subhead{color:\"{{foreground-2}}\"}md-chips.md-THEME_NAME-theme .md-chips{box-shadow:0 1px \"{{foreground-4}}\"}md-chips.md-THEME_NAME-theme .md-chips.md-focused{box-shadow:0 2px \"{{primary-color}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input{color:\"{{foreground-1}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input:-moz-placeholder,md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::-moz-placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input:-ms-input-placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme .md-chips .md-chip-input-container input::-webkit-input-placeholder{color:\"{{foreground-3}}\"}md-chips.md-THEME_NAME-theme md-chip{background:\"{{background-300}}\";color:\"{{background-800}}\"}md-chips.md-THEME_NAME-theme md-chip md-icon{color:\"{{background-700}}\"}md-chips.md-THEME_NAME-theme md-chip.md-focused{background:\"{{primary-color}}\";color:\"{{primary-contrast}}\"}md-chips.md-THEME_NAME-theme md-chip.md-focused md-icon{color:\"{{primary-contrast}}\"}md-chips.md-THEME_NAME-theme md-chip._md-chip-editing{background:transparent;color:\"{{background-800}}\"}md-chips.md-THEME_NAME-theme md-chip-remove .md-button md-icon path{fill:\"{{background-500}}\"}.md-contact-suggestion span.md-contact-email{color:\"{{background-400}}\"}md-checkbox.md-THEME_NAME-theme .md-ripple{color:\"{{accent-A700}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-ripple{color:\"{{background-600}}\"}md-checkbox.md-THEME_NAME-theme.md-checked.md-focused .md-container:before{background-color:\"{{accent-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-ink-ripple{color:\"{{accent-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-icon{background-color:\"{{accent-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme.md-checked .md-icon:after{border-color:\"{{accent-contrast-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-ripple{color:\"{{primary-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ripple{color:\"{{background-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple{color:\"{{primary-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-icon{background-color:\"{{primary-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked.md-focused .md-container:before{background-color:\"{{primary-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-icon:after{border-color:\"{{primary-contrast-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-primary .md-indeterminate[disabled] .md-container{color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn .md-ripple{color:\"{{warn-600}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn .md-ink-ripple{color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple{color:\"{{warn-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-icon{background-color:\"{{warn-color-0.87}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked.md-focused:not([disabled]) .md-container:before{background-color:\"{{warn-color-0.26}}\"}md-checkbox.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-icon:after{border-color:\"{{background-200}}\"}md-checkbox.md-THEME_NAME-theme[disabled]:not(.md-checked) .md-icon{border-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled].md-checked .md-icon{background-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled].md-checked .md-icon:after{border-color:\"{{background-200}}\"}md-checkbox.md-THEME_NAME-theme[disabled] .md-icon:after{border-color:\"{{foreground-3}}\"}md-checkbox.md-THEME_NAME-theme[disabled] .md-label{color:\"{{foreground-3}}\"}md-content.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-default}}\"}.md-calendar.md-THEME_NAME-theme{background:\"{{background-A100}}\";color:\"{{background-A200-0.87}}\"}.md-calendar.md-THEME_NAME-theme tr:last-child td{border-bottom-color:\"{{background-200}}\"}.md-THEME_NAME-theme .md-calendar-day-header{background:\"{{background-300}}\";color:\"{{background-A200-0.87}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-date-today .md-calendar-date-selection-indicator{border:1px solid \"{{primary-500}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-date-today.md-calendar-date-disabled{color:\"{{primary-500-0.6}}\"}.md-calendar-date.md-focus .md-THEME_NAME-theme .md-calendar-date-selection-indicator,.md-THEME_NAME-theme .md-calendar-date-selection-indicator:hover{background:\"{{background-300}}\"}.md-THEME_NAME-theme .md-calendar-date.md-calendar-selected-date .md-calendar-date-selection-indicator,.md-THEME_NAME-theme .md-calendar-date.md-focus.md-calendar-selected-date .md-calendar-date-selection-indicator{background:\"{{primary-500}}\";color:\"{{primary-500-contrast}}\";border-color:transparent}.md-THEME_NAME-theme .md-calendar-date-disabled,.md-THEME_NAME-theme .md-calendar-month-label-disabled{color:\"{{background-A200-0.435}}\"}.md-THEME_NAME-theme .md-datepicker-input{color:\"{{foreground-1}}\"}.md-THEME_NAME-theme .md-datepicker-input:-moz-placeholder,.md-THEME_NAME-theme .md-datepicker-input::-moz-placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input:-ms-input-placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input::-webkit-input-placeholder{color:\"{{foreground-3}}\"}.md-THEME_NAME-theme .md-datepicker-input-container{border-bottom-color:\"{{foreground-4}}\"}.md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{primary-color}}\"}.md-accent .md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{accent-color}}\"}.md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-invalid,.md-warn .md-THEME_NAME-theme .md-datepicker-input-container.md-datepicker-focused{border-bottom-color:\"{{warn-A700}}\"}.md-THEME_NAME-theme .md-datepicker-calendar-pane{border-color:\"{{background-hue-1}}\"}.md-THEME_NAME-theme .md-datepicker-triangle-button .md-datepicker-expand-triangle{border-top-color:\"{{foreground-2}}\"}.md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon{color:\"{{primary-color}}\"}.md-accent .md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon,.md-THEME_NAME-theme .md-datepicker-open.md-accent .md-datepicker-calendar-icon{color:\"{{accent-color}}\"}.md-THEME_NAME-theme .md-datepicker-open.md-warn .md-datepicker-calendar-icon,.md-warn .md-THEME_NAME-theme .md-datepicker-open .md-datepicker-calendar-icon{color:\"{{warn-A700}}\"}.md-THEME_NAME-theme .md-datepicker-calendar{background:\"{{background-A100}}\"}.md-THEME_NAME-theme .md-datepicker-input-mask-opaque{box-shadow:0 0 0 9999px \"{{background-hue-1}}\"}.md-THEME_NAME-theme .md-datepicker-open .md-datepicker-input-container{background:\"{{background-hue-1}}\"}md-dialog.md-THEME_NAME-theme{border-radius:4px;background-color:\"{{background-hue-1}}\";color:\"{{foreground-1}}\"}md-dialog.md-THEME_NAME-theme.md-content-overflow .md-actions,md-dialog.md-THEME_NAME-theme.md-content-overflow md-dialog-actions,md-divider.md-THEME_NAME-theme{border-top-color:\"{{foreground-4}}\"}.layout-gt-lg-row>md-divider.md-THEME_NAME-theme,.layout-gt-md-row>md-divider.md-THEME_NAME-theme,.layout-gt-sm-row>md-divider.md-THEME_NAME-theme,.layout-gt-xs-row>md-divider.md-THEME_NAME-theme,.layout-lg-row>md-divider.md-THEME_NAME-theme,.layout-md-row>md-divider.md-THEME_NAME-theme,.layout-row>md-divider.md-THEME_NAME-theme,.layout-sm-row>md-divider.md-THEME_NAME-theme,.layout-xl-row>md-divider.md-THEME_NAME-theme,.layout-xs-row>md-divider.md-THEME_NAME-theme{border-right-color:\"{{foreground-4}}\"}md-icon.md-THEME_NAME-theme{color:\"{{foreground-2}}\"}md-icon.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}md-icon.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}md-icon.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}md-input-container.md-THEME_NAME-theme .md-input{color:\"{{foreground-1}}\";border-color:\"{{foreground-4}}\"}md-input-container.md-THEME_NAME-theme .md-input:-moz-placeholder,md-input-container.md-THEME_NAME-theme .md-input::-moz-placeholder{color:\"{{foreground-3}}\"}md-input-container.md-THEME_NAME-theme .md-input:-ms-input-placeholder{color:\"{{foreground-3}}\"}md-input-container.md-THEME_NAME-theme .md-input::-webkit-input-placeholder{color:\"{{foreground-3}}\"}md-input-container.md-THEME_NAME-theme>md-icon{color:\"{{foreground-1}}\"}md-input-container.md-THEME_NAME-theme .md-placeholder,md-input-container.md-THEME_NAME-theme label{color:\"{{foreground-3}}\"}md-input-container.md-THEME_NAME-theme label.md-required:after{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-focused):not(.md-input-invalid) label.md-required:after{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme .md-input-message-animation,md-input-container.md-THEME_NAME-theme .md-input-messages-animation{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme .md-input-message-animation .md-char-counter,md-input-container.md-THEME_NAME-theme .md-input-messages-animation .md-char-counter{color:\"{{foreground-1}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input:-moz-placeholder,md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::-moz-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input:-ms-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme.md-input-focused .md-input::-webkit-input-placeholder{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-has-value label{color:\"{{foreground-2}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused .md-input,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-resized .md-input{border-color:\"{{primary-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused md-icon{color:\"{{primary-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent .md-input{border-color:\"{{accent-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-accent md-icon{color:\"{{accent-color}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn .md-input{border-color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn label,md-input-container.md-THEME_NAME-theme:not(.md-input-invalid).md-input-focused.md-warn md-icon{color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme.md-input-invalid .md-input{border-color:\"{{warn-A700}}\"}md-input-container.md-THEME_NAME-theme.md-input-invalid .md-char-counter,md-input-container.md-THEME_NAME-theme.md-input-invalid .md-input-message-animation,md-input-container.md-THEME_NAME-theme.md-input-invalid label{color:\"{{warn-A700}}\"}[disabled] md-input-container.md-THEME_NAME-theme .md-input,md-input-container.md-THEME_NAME-theme .md-input[disabled]{border-bottom-color:transparent;color:\"{{foreground-3}}\";background-image:linear-gradient(90deg,\"{{foreground-3}}\" 0,\"{{foreground-3}}\" 33%,transparent 0);background-image:-ms-linear-gradient(left,transparent 0,\"{{foreground-3}}\" 100%)}md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text h3,md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text h4,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text h3,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text h4{color:\"{{foreground-1}}\"}md-list.md-THEME_NAME-theme md-list-item.md-2-line .md-list-item-text p,md-list.md-THEME_NAME-theme md-list-item.md-3-line .md-list-item-text p{color:\"{{foreground-2}}\"}md-list.md-THEME_NAME-theme .md-proxy-focus.md-focused div.md-no-style{background-color:\"{{background-100}}\"}md-list.md-THEME_NAME-theme md-list-item .md-avatar-icon{background-color:\"{{foreground-3}}\";color:\"{{background-color}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon{color:\"{{foreground-2}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon.md-highlight{color:\"{{primary-color}}\"}md-list.md-THEME_NAME-theme md-list-item>md-icon.md-highlight.md-accent{color:\"{{accent-color}}\"}md-menu-content.md-THEME_NAME-theme{background-color:\"{{background-A100}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item{color:\"{{background-A200-0.87}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item md-icon{color:\"{{background-A200-0.54}}\"}md-menu-content.md-THEME_NAME-theme md-menu-item .md-button[disabled],md-menu-content.md-THEME_NAME-theme md-menu-item .md-button[disabled] md-icon{color:\"{{background-A200-0.25}}\"}md-menu-content.md-THEME_NAME-theme md-menu-divider{background-color:\"{{background-A200-0.11}}\"}md-menu-bar.md-THEME_NAME-theme>button.md-button{color:\"{{foreground-2}}\";border-radius:2px}md-menu-bar.md-THEME_NAME-theme md-menu.md-open>button,md-menu-bar.md-THEME_NAME-theme md-menu>button:focus{outline:none;background:\"{{background-200}}\"}md-menu-bar.md-THEME_NAME-theme.md-open:not(.md-keyboard-mode) md-menu:hover>button{background-color:\"{{ background-500-0.2}}\"}md-menu-bar.md-THEME_NAME-theme:not(.md-keyboard-mode):not(.md-open) md-menu button:focus,md-menu-bar.md-THEME_NAME-theme:not(.md-keyboard-mode):not(.md-open) md-menu button:hover{background:transparent}md-menu-content.md-THEME_NAME-theme .md-menu>.md-button:after{color:\"{{background-A200-0.54}}\"}md-menu-content.md-THEME_NAME-theme .md-menu.md-open>.md-button{background-color:\"{{ background-500-0.2}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar{background-color:\"{{background-A100}}\";color:\"{{background-A200}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar md-toolbar-filler{background-color:\"{{primary-color}}\";color:\"{{background-A100-0.87}}\"}md-toolbar.md-THEME_NAME-theme.md-menu-toolbar md-toolbar-filler md-icon{color:\"{{background-A100-0.87}}\"}md-nav-bar.md-THEME_NAME-theme .md-nav-bar{background-color:transparent;border-color:\"{{foreground-4}}\"}md-nav-bar.md-THEME_NAME-theme .md-button._md-nav-button.md-unselected{color:\"{{foreground-2}}\"}md-nav-bar.md-THEME_NAME-theme md-nav-ink-bar{color:\"{{accent-color}}\";background:\"{{accent-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar{background-color:\"{{accent-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button{color:\"{{accent-A100}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{accent-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{accent-contrast-0.1}}\"}md-nav-bar.md-THEME_NAME-theme.md-accent>.md-nav-bar md-nav-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar{background-color:\"{{warn-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button{color:\"{{warn-100}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{warn-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-warn>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{warn-contrast-0.1}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar{background-color:\"{{primary-color}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button{color:\"{{primary-100}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-active,md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{primary-contrast}}\"}md-nav-bar.md-THEME_NAME-theme.md-primary>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{primary-color}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{primary-100}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{primary-contrast}}\"}md-toolbar>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{accent-color}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{accent-A100}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{accent-contrast}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{accent-contrast-0.1}}\"}md-toolbar.md-accent>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar md-nav-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar{background-color:\"{{warn-color}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button{color:\"{{warn-100}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-active,md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{color:\"{{warn-contrast}}\"}md-toolbar.md-warn>md-nav-bar.md-THEME_NAME-theme>.md-nav-bar .md-button._md-nav-button.md-focused{background:\"{{warn-contrast-0.1}}\"}._md-panel-backdrop.md-THEME_NAME-theme{background-color:\"{{background-900-1.0}}\"}md-progress-circular.md-THEME_NAME-theme path{stroke:\"{{primary-color}}\"}md-progress-circular.md-THEME_NAME-theme.md-warn path{stroke:\"{{warn-color}}\"}md-progress-circular.md-THEME_NAME-theme.md-accent path{stroke:\"{{accent-color}}\"}md-progress-linear.md-THEME_NAME-theme .md-container{background-color:\"{{primary-100}}\"}md-progress-linear.md-THEME_NAME-theme .md-bar{background-color:\"{{primary-color}}\"}md-progress-linear.md-THEME_NAME-theme.md-warn .md-container{background-color:\"{{warn-100}}\"}md-progress-linear.md-THEME_NAME-theme.md-warn .md-bar{background-color:\"{{warn-color}}\"}md-progress-linear.md-THEME_NAME-theme.md-accent .md-container{background-color:\"{{accent-100}}\"}md-progress-linear.md-THEME_NAME-theme.md-accent .md-bar{background-color:\"{{accent-color}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-primary .md-bar1{background-color:\"{{primary-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-primary .md-dashed:before{background:radial-gradient(\"{{primary-100}}\" 0,\"{{primary-100}}\" 16%,transparent 42%)}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-warn .md-bar1{background-color:\"{{warn-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-warn .md-dashed:before{background:radial-gradient(\"{{warn-100}}\" 0,\"{{warn-100}}\" 16%,transparent 42%)}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-accent .md-bar1{background-color:\"{{accent-100}}\"}md-progress-linear.md-THEME_NAME-theme[md-mode=buffer].md-accent .md-dashed:before{background:radial-gradient(\"{{accent-100}}\" 0,\"{{accent-100}}\" 16%,transparent 42%)}md-radio-button.md-THEME_NAME-theme .md-off{border-color:\"{{foreground-2}}\"}md-radio-button.md-THEME_NAME-theme .md-on{background-color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme.md-checked .md-off{border-color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme.md-checked .md-ink-ripple{color:\"{{accent-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme .md-container .md-ripple{color:\"{{accent-A700}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-on,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-on{background-color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-off{border-color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-checked .md-ink-ripple{color:\"{{primary-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-primary .md-container .md-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-primary .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-primary .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-primary .md-container .md-ripple{color:\"{{primary-600}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-on,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-on,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-on{background-color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-off,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-off,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-off{border-color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-ink-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn.md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-checked .md-ink-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-checked .md-ink-ripple{color:\"{{warn-color-0.87}}\"}md-radio-button.md-THEME_NAME-theme:not([disabled]).md-warn .md-container .md-ripple,md-radio-button.md-THEME_NAME-theme:not([disabled]) .md-warn .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]).md-warn .md-container .md-ripple,md-radio-group.md-THEME_NAME-theme:not([disabled]) .md-warn .md-container .md-ripple{color:\"{{warn-600}}\"}md-radio-button.md-THEME_NAME-theme[disabled],md-radio-group.md-THEME_NAME-theme[disabled]{color:\"{{foreground-3}}\"}md-radio-button.md-THEME_NAME-theme[disabled] .md-container .md-off,md-radio-button.md-THEME_NAME-theme[disabled] .md-container .md-on,md-radio-group.md-THEME_NAME-theme[disabled] .md-container .md-off,md-radio-group.md-THEME_NAME-theme[disabled] .md-container .md-on{border-color:\"{{foreground-3}}\"}md-radio-group.md-THEME_NAME-theme .md-checked .md-ink-ripple{color:\"{{accent-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme .md-checked:not([disabled]).md-primary .md-ink-ripple,md-radio-group.md-THEME_NAME-theme.md-primary .md-checked:not([disabled]) .md-ink-ripple{color:\"{{primary-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme .md-checked.md-primary .md-ink-ripple{color:\"{{warn-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked .md-container:before{background-color:\"{{accent-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked.md-primary .md-container:before,md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty).md-primary .md-checked .md-container:before{background-color:\"{{primary-color-0.26}}\"}md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty) .md-checked.md-warn .md-container:before,md-radio-group.md-THEME_NAME-theme.md-focused:not(:empty).md-warn .md-checked .md-container:before{background-color:\"{{warn-color-0.26}}\"}md-input-container md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{warn-A700}}\"}md-input-container:not(.md-input-focused):not(.md-input-invalid) md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{foreground-3}}\"}md-input-container.md-input-focused:not(.md-input-has-value) md-select.md-THEME_NAME-theme .md-select-value,md-input-container.md-input-focused:not(.md-input-has-value) md-select.md-THEME_NAME-theme .md-select-value.md-select-placeholder{color:\"{{primary-color}}\"}md-input-container.md-input-invalid md-select.md-THEME_NAME-theme .md-select-value{color:\"{{warn-A700}}\"!important;border-bottom-color:\"{{warn-A700}}\"!important}md-input-container.md-input-invalid md-select.md-THEME_NAME-theme.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme[disabled] .md-select-value{border-bottom-color:transparent;background-image:linear-gradient(90deg,\"{{foreground-3}}\" 0,\"{{foreground-3}}\" 33%,transparent 0);background-image:-ms-linear-gradient(left,transparent 0,\"{{foreground-3}}\" 100%)}md-select.md-THEME_NAME-theme .md-select-value{border-bottom-color:\"{{foreground-4}}\"}md-select.md-THEME_NAME-theme .md-select-value.md-select-placeholder{color:\"{{foreground-3}}\"}md-select.md-THEME_NAME-theme .md-select-value span:first-child:after{color:\"{{warn-A700}}\"}md-select.md-THEME_NAME-theme.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme.ng-invalid.ng-touched .md-select-value{color:\"{{warn-A700}}\"!important;border-bottom-color:\"{{warn-A700}}\"!important}md-select.md-THEME_NAME-theme.ng-invalid.ng-touched.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme:not([disabled]):focus .md-select-value{border-bottom-color:\"{{primary-color}}\";color:\"{{ foreground-1 }}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus .md-select-value.md-select-placeholder{color:\"{{ foreground-1 }}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-no-underline .md-select-value{border-bottom-color:transparent!important}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-accent .md-select-value{border-bottom-color:\"{{accent-color}}\"}md-select.md-THEME_NAME-theme:not([disabled]):focus.md-warn .md-select-value{border-bottom-color:\"{{warn-color}}\"}md-select.md-THEME_NAME-theme[disabled] .md-select-icon,md-select.md-THEME_NAME-theme[disabled] .md-select-value,md-select.md-THEME_NAME-theme[disabled] .md-select-value.md-select-placeholder{color:\"{{foreground-3}}\"}md-select.md-THEME_NAME-theme .md-select-icon{color:\"{{foreground-2}}\"}md-select-menu.md-THEME_NAME-theme md-content{background:\"{{background-A100}}\"}md-select-menu.md-THEME_NAME-theme md-content md-optgroup{color:\"{{background-600-0.87}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option{color:\"{{background-900-0.87}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[disabled] .md-text{color:\"{{background-400-0.87}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option:not([disabled]):focus,md-select-menu.md-THEME_NAME-theme md-content md-option:not([disabled]):hover{background:\"{{background-200}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected]{color:\"{{primary-500}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected]:focus{color:\"{{primary-600}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-accent{color:\"{{accent-color}}\"}md-select-menu.md-THEME_NAME-theme md-content md-option[selected].md-accent:focus{color:\"{{accent-A700}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-ripple{color:\"{{primary-600}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-ripple{color:\"{{background-600}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-ink-ripple{color:\"{{foreground-2}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-ink-ripple{color:\"{{primary-color-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme:not(.md-checked) .md-icon{border-color:\"{{foreground-2}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-icon{background-color:\"{{primary-color-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected].md-focused .md-container:before{background-color:\"{{primary-color-0.26}}\"}.md-checkbox-enabled.md-THEME_NAME-theme[selected] .md-icon:after{border-color:\"{{primary-contrast-0.87}}\"}.md-checkbox-enabled.md-THEME_NAME-theme .md-indeterminate[disabled] .md-container{color:\"{{foreground-3}}\"}.md-checkbox-enabled.md-THEME_NAME-theme md-option .md-text{color:\"{{background-900-0.87}}\"}md-sidenav.md-THEME_NAME-theme,md-sidenav.md-THEME_NAME-theme md-content{background-color:\"{{background-hue-1}}\"}md-slider.md-THEME_NAME-theme .md-track{background-color:\"{{foreground-3}}\"}md-slider.md-THEME_NAME-theme .md-track-ticks{color:\"{{background-contrast}}\"}md-slider.md-THEME_NAME-theme .md-focus-ring{background-color:\"{{accent-A200-0.2}}\"}md-slider.md-THEME_NAME-theme .md-disabled-thumb{border-color:\"{{background-color}}\";background-color:\"{{background-color}}\"}md-slider.md-THEME_NAME-theme.md-min .md-thumb:after{background-color:\"{{background-color}}\";border-color:\"{{foreground-3}}\"}md-slider.md-THEME_NAME-theme.md-min .md-focus-ring{background-color:\"{{foreground-3-0.38}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-thumb:after{background-color:\"{{background-contrast}}\";border-color:transparent}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-sign{background-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete] .md-sign:after{border-top-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme.md-min[md-discrete][md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme .md-track.md-track-fill{background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-thumb:after{border-color:\"{{accent-color}}\";background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-sign{background-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-sign:after{border-top-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{accent-color}}\"}md-slider.md-THEME_NAME-theme .md-thumb-text{color:\"{{accent-contrast}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-focus-ring{background-color:\"{{warn-200-0.38}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-track.md-track-fill{background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-thumb:after{border-color:\"{{warn-color}}\";background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-sign{background-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-sign:after{border-top-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{warn-color}}\"}md-slider.md-THEME_NAME-theme.md-warn .md-thumb-text{color:\"{{warn-contrast}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-focus-ring{background-color:\"{{primary-200-0.38}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-track.md-track-fill{background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-thumb:after{border-color:\"{{primary-color}}\";background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-sign{background-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-sign:after{border-top-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary[md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{primary-color}}\"}md-slider.md-THEME_NAME-theme.md-primary .md-thumb-text{color:\"{{primary-contrast}}\"}md-slider.md-THEME_NAME-theme[disabled] .md-thumb:after{border-color:transparent}md-slider.md-THEME_NAME-theme[disabled]:not(.md-min) .md-thumb:after,md-slider.md-THEME_NAME-theme[disabled][md-discrete] .md-thumb:after{background-color:\"{{foreground-3}}\";border-color:transparent}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-sign{background-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-sign:after{border-top-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly][md-vertical] .md-sign:after{border-top-color:transparent;border-left-color:\"{{background-400}}\"}md-slider.md-THEME_NAME-theme[disabled][readonly] .md-disabled-thumb{border-color:transparent;background-color:transparent}md-slider-container[disabled]>:first-child:not(md-slider),md-slider-container[disabled]>:last-child:not(md-slider){color:\"{{foreground-3}}\"}.md-subheader.md-THEME_NAME-theme{color:\"{{ foreground-2-0.23 }}\";background-color:\"{{background-default}}\"}.md-subheader.md-THEME_NAME-theme.md-primary{color:\"{{primary-color}}\"}.md-subheader.md-THEME_NAME-theme.md-accent{color:\"{{accent-color}}\"}.md-subheader.md-THEME_NAME-theme.md-warn{color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme .md-ink-ripple{color:\"{{background-500}}\"}md-switch.md-THEME_NAME-theme .md-thumb{background-color:\"{{background-50}}\"}md-switch.md-THEME_NAME-theme .md-bar{background-color:\"{{background-500}}\"}md-switch.md-THEME_NAME-theme.md-checked .md-ink-ripple{color:\"{{accent-color}}\"}md-switch.md-THEME_NAME-theme.md-checked .md-thumb{background-color:\"{{accent-color}}\"}md-switch.md-THEME_NAME-theme.md-checked .md-bar{background-color:\"{{accent-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-focused .md-thumb:before{background-color:\"{{accent-color-0.26}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-primary .md-ink-ripple{color:\"{{primary-color}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-primary .md-thumb{background-color:\"{{primary-color}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-primary .md-bar{background-color:\"{{primary-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-primary.md-focused .md-thumb:before{background-color:\"{{primary-color-0.26}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-warn .md-ink-ripple{color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-warn .md-thumb{background-color:\"{{warn-color}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-warn .md-bar{background-color:\"{{warn-color-0.5}}\"}md-switch.md-THEME_NAME-theme.md-checked.md-warn.md-focused .md-thumb:before{background-color:\"{{warn-color-0.26}}\"}md-switch.md-THEME_NAME-theme[disabled] .md-thumb{background-color:\"{{background-400}}\"}md-switch.md-THEME_NAME-theme[disabled] .md-bar{background-color:\"{{foreground-4}}\"}md-tabs.md-THEME_NAME-theme md-tabs-wrapper{background-color:transparent;border-color:\"{{foreground-4}}\"}md-tabs.md-THEME_NAME-theme .md-paginator md-icon{color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme md-ink-bar{color:\"{{accent-color}}\";background:\"{{accent-color}}\"}md-tabs.md-THEME_NAME-theme .md-tab{color:\"{{foreground-2}}\"}md-tabs.md-THEME_NAME-theme .md-tab[disabled],md-tabs.md-THEME_NAME-theme .md-tab[disabled] md-icon{color:\"{{foreground-3}}\"}md-tabs.md-THEME_NAME-theme .md-tab.md-active,md-tabs.md-THEME_NAME-theme .md-tab.md-active md-icon,md-tabs.md-THEME_NAME-theme .md-tab.md-focused,md-tabs.md-THEME_NAME-theme .md-tab.md-focused md-icon{color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme .md-tab.md-focused{background:\"{{primary-color-0.1}}\"}md-tabs.md-THEME_NAME-theme .md-tab .md-ripple-container{color:\"{{accent-A100}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper{background-color:\"{{accent-color}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{accent-A100}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{accent-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{accent-contrast-0.1}}\"}md-tabs.md-THEME_NAME-theme.md-accent>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper{background-color:\"{{primary-color}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{primary-100}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{primary-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-primary>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{primary-contrast-0.1}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper{background-color:\"{{warn-color}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{warn-100}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{warn-contrast}}\"}md-tabs.md-THEME_NAME-theme.md-warn>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{warn-contrast-0.1}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{primary-color}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{primary-100}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{primary-contrast}}\"}md-toolbar>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{primary-contrast-0.1}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{accent-color}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{accent-A100}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{accent-contrast}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{accent-contrast-0.1}}\"}md-toolbar.md-accent>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-ink-bar{color:\"{{primary-600-1}}\";background:\"{{primary-600-1}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper{background-color:\"{{warn-color}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]),md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]) md-icon{color:\"{{warn-100}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-active md-icon,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused,md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused md-icon{color:\"{{warn-contrast}}\"}md-toolbar.md-warn>md-tabs.md-THEME_NAME-theme>md-tabs-wrapper>md-tabs-canvas>md-pagination-wrapper>md-tab-item:not([disabled]).md-focused{background:\"{{warn-contrast-0.1}}\"}md-toast.md-THEME_NAME-theme .md-toast-content{background-color:#323232;color:\"{{background-50}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button{color:\"{{background-50}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight{color:\"{{accent-color}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight.md-primary{color:\"{{primary-color}}\"}md-toast.md-THEME_NAME-theme .md-toast-content .md-button.md-highlight.md-warn{color:\"{{warn-color}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar){background-color:\"{{primary-color}}\";color:\"{{primary-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar) md-icon{color:\"{{primary-contrast}}\";fill:\"{{primary-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar) .md-button[disabled] md-icon{color:\"{{primary-contrast-0.26}}\";fill:\"{{primary-contrast-0.26}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent{background-color:\"{{accent-color}}\";color:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent .md-ink-ripple{color:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent md-icon{color:\"{{accent-contrast}}\";fill:\"{{accent-contrast}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-accent .md-button[disabled] md-icon{color:\"{{accent-contrast-0.26}}\";fill:\"{{accent-contrast-0.26}}\"}md-toolbar.md-THEME_NAME-theme:not(.md-menu-toolbar).md-warn{background-color:\"{{warn-color}}\";color:\"{{warn-contrast}}\"}.md-panel.md-tooltip.md-THEME_NAME-theme{color:\"{{background-700-contrast}}\";background-color:\"{{background-700}}\"}body.md-THEME_NAME-theme,html.md-THEME_NAME-theme{color:\"{{foreground-1}}\";background-color:\"{{background-color}}\"}"); 
})();


})(window, window.angular);;window.ngMaterial={version:{full: "1.1.5"}};
"undefined"!=typeof module&&"undefined"!=typeof exports&&module.exports===exports&&(module.exports="angularSpinners"),function(e,n,r){n.module("angularSpinners",[]).factory("spinnerService",function(){var e={};return{_register:function(n){if(!n.hasOwnProperty("name"))throw new Error("Spinner must specify a name when registering with the spinner service.");e[n.name]=n},_unregister:function(n){e.hasOwnProperty(n)&&delete e[n]},_unregisterGroup:function(n){for(var r in e)e[r].group===n&&delete e[r]},_unregisterAll:function(){for(var n in e)delete e[n]},show:function(n){var r=e[n];if(!r)throw new Error("No spinner named '"+n+"' is registered.");r.show()},hide:function(n){var r=e[n];if(!r)throw new Error("No spinner named '"+n+"' is registered.");r.hide()},showGroup:function(n){var r=!1;for(var o in e){var i=e[o];i.group===n&&(i.show(),r=!0)}if(!r)throw new Error("No spinners found with group '"+n+"'.")},hideGroup:function(n){var r=!1;for(var o in e){var i=e[o];i.group===n&&(i.hide(),r=!0)}if(!r)throw new Error("No spinners found with group '"+n+"'.")},showAll:function(){for(var n in e)e[n].show()},hideAll:function(){for(var n in e)e[n].hide()}}}),n.module("angularSpinners").directive("spinner",function(){return{restrict:"EA",replace:!0,transclude:!0,scope:{name:"@?",group:"@?",show:"=?",imgSrc:"@?",register:"@?",onLoaded:"&?",onShow:"&?",onHide:"&?"},template:['<div ng-show="show">','  <img ng-if="imgSrc" ng-src="{{imgSrc}}" />',"  <ng-transclude></ng-transclude>","</div>"].join(""),controller:["$scope","spinnerService",function(e,n){e.register=e.hasOwnProperty("register")&&"false"===e.register.toLowerCase()?!1:!0;var r={name:e.name,group:e.group,show:function(){e.show=!0},hide:function(){e.show=!1},toggle:function(){e.show=!e.show}};e.register===!0&&n._register(r),(e.onShow||e.onHide)&&e.$watch("show",function(o){o&&e.onShow?e.onShow({spinnerService:n,spinnerApi:r}):!o&&e.onHide&&e.onHide({spinnerService:n,spinnerApi:r})}),e.onLoaded&&e.onLoaded({spinnerService:n,spinnerApi:r})}]}})}(window,window.angular);
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = global || self, global.sha1 = factory());
}(this, (function () { 'use strict';

  /**
   * @file This is a SHA-1 hash generator by JavaScript.
   * @author Hsun
   * @description For your convenience, the code comments have been translated by Google.
   ***/

  // 消息填充位，补足长度。
  // Message padding bits, complement the length.
  function fillString(str) {
    var blockAmount = ((str.length + 8) >> 6) + 1,
      blocks = [],
      i;

    for (i = 0; i < blockAmount * 16; i++) {
      blocks[i] = 0;
    }
    for (i = 0; i < str.length; i++) {
      blocks[i >> 2] |= str.charCodeAt(i) << (24 - (i & 3) * 8);
    }
    blocks[i >> 2] |= 0x80 << (24 - (i & 3) * 8);
    blocks[blockAmount * 16 - 1] = str.length * 8;

    return blocks;
  }

  // 将输入的二进制数组转化为十六进制的字符串。
  // Convert the input binary array to a hexadecimal string.
  function binToHex(binArray) {
    var hexString = "0123456789abcdef",
      str = "",
      i;

    for (i = 0; i < binArray.length * 4; i++) {
      str += hexString.charAt((binArray[i >> 2] >> ((3 - i % 4) * 8 + 4)) & 0xF) +
        hexString.charAt((binArray[i >> 2] >> ((3 - i % 4) * 8)) & 0xF);
    }

    return str;
  }

  // 核心函数，输出为长度为5的number数组，对应160位的消息摘要。
  // The core function, the output is a number array with a length of 5,
  // corresponding to a 160-bit message digest.
  function core(blockArray) {
    var w = [],
      a = 0x67452301,
      b = 0xEFCDAB89,
      c = 0x98BADCFE,
      d = 0x10325476,
      e = 0xC3D2E1F0,
      olda,
      oldb,
      oldc,
      oldd,
      olde,
      t,
      i,
      j;

    for (i = 0; i < blockArray.length; i += 16) {  //每次处理512位 16*32
      olda = a;
      oldb = b;
      oldc = c;
      oldd = d;
      olde = e;

      for (j = 0; j < 80; j++) {  //对每个512位进行80步操作
        if (j < 16) {
          w[j] = blockArray[i + j];
        } else {
          w[j] = cyclicShift(w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16], 1);
        }
        t = modPlus(modPlus(cyclicShift(a, 5), ft(j, b, c, d)), modPlus(modPlus(e, w[j]), kt(j)));
        e = d;
        d = c;
        c = cyclicShift(b, 30);
        b = a;
        a = t;
      }

      a = modPlus(a, olda);
      b = modPlus(b, oldb);
      c = modPlus(c, oldc);
      d = modPlus(d, oldd);
      e = modPlus(e, olde);
    }

    return [a, b, c, d, e];
  }

  // 根据t值返回相应得压缩函数中用到的f函数。
  // According to the t value, return the corresponding f function used in
  // the compression function.
  function ft(t, b, c, d) {
    if (t < 20) {
      return (b & c) | ((~b) & d);
    } else if (t < 40) {
      return b ^ c ^ d;
    } else if (t < 60) {
      return (b & c) | (b & d) | (c & d);
    } else {
      return b ^ c ^ d;
    }
  }

  // 根据t值返回相应得压缩函数中用到的K值。
  // According to the t value, return the corresponding K value used in
  // the compression function.
  function kt(t) {
    return (t < 20) ? 0x5A827999 :
      (t < 40) ? 0x6ED9EBA1 :
        (t < 60) ? 0x8F1BBCDC : 0xCA62C1D6;
  }

  // 模2的32次方加法，因为JavaScript的number是双精度浮点数表示，所以将32位数拆成高16位和低16位分别进行相加
  // Modulo 2 to the 32nd power addition, because JavaScript's number is a
  // double-precision floating-point number, so the 32-bit number is split
  // into the upper 16 bits and the lower 16 bits are added separately.
  function modPlus(x, y) {
    var low = (x & 0xFFFF) + (y & 0xFFFF),
      high = (x >> 16) + (y >> 16) + (low >> 16);

    return (high << 16) | (low & 0xFFFF);
  }

  // 对输入的32位的num二进制数进行循环左移 ,因为JavaScript的number是双精度浮点数表示，所以移位需需要注意
  // Rotate left of the input 32-bit num binary number, because JavaScript's
  // number is a double-precision floating-point number, so you need to pay
  //  attention to the shift.
  function cyclicShift(num, k) {
    return (num << k) | (num >>> (32 - k));
  }

  // 主函数根据输入的消息字符串计算消息摘要，返回十六进制表示的消息摘要
  // The main function calculates the message digest based on the input message
  // string and returns the message digest in hexadecimal.
  function sha1(s) {
    return binToHex(core(fillString(s)));
  }

  return sha1;

})));

/**
 * @license Angulartics
 * (c) 2013 Luis Farzati http://angulartics.github.io/
 * License: MIT
 */
(function(angular, analytics) {
'use strict';

var angulartics = window.angulartics || (window.angulartics = {});
angulartics.waitForVendorCount = 0;
angulartics.waitForVendorApi = function (objectName, delay, containsField, registerFn, onTimeout) {
  if (!onTimeout) { angulartics.waitForVendorCount++; }
  if (!registerFn) { registerFn = containsField; containsField = undefined; }
  if (!Object.prototype.hasOwnProperty.call(window, objectName) || (containsField !== undefined && window[objectName][containsField] === undefined)) {
    setTimeout(function () { angulartics.waitForVendorApi(objectName, delay, containsField, registerFn, true); }, delay);
  }
  else {
    angulartics.waitForVendorCount--;
    registerFn(window[objectName]);
  }
};

/**
 * @ngdoc overview
 * @name angulartics
 */
angular.module('angulartics', [])
.provider('$analytics', $analytics)
.run(['$rootScope', '$window', '$analytics', '$injector', $analyticsRun])
.directive('analyticsOn', ['$analytics', analyticsOn])
.config(['$provide', exceptionTrack]);

function $analytics() {
  var vm = this;

  var settings = {
    pageTracking: {
      autoTrackFirstPage: true,
      autoTrackVirtualPages: true,
      trackRelativePath: false,
      trackRoutes: true,
      trackStates: true,
      autoBasePath: false,
      basePath: '',
      excludedRoutes: [],
      queryKeysWhitelisted: [],
      queryKeysBlacklisted: [],
      filterUrlSegments: []
    },
    eventTracking: {},
    bufferFlushDelay: 1000, // Support only one configuration for buffer flush delay to simplify buffering
    trackExceptions: false,
    optOut: false,
    developerMode: false // Prevent sending data in local/development environment
  };

  // List of known handlers that plugins can register themselves for
  var knownHandlers = [
    'pageTrack',
    'eventTrack',
    'exceptionTrack',
    'transactionTrack',
    'setAlias',
    'setUsername',
    'setUserProperties',
    'setUserPropertiesOnce',
    'setSuperProperties',
    'setSuperPropertiesOnce',
    'incrementProperty',
    'userTimings',
    'clearCookies'
  ];
  // Cache and handler properties will match values in 'knownHandlers' as the buffering functons are installed.
  var cache = {};
  var handlers = {};
  var handlerOptions = {};

  // General buffering handler
  function bufferedHandler(handlerName){
    return function(){
      if(angulartics.waitForVendorCount){
        if(!cache[handlerName]){ cache[handlerName] = []; }
        cache[handlerName].push(arguments);
      }
    };
  }

  // As handlers are installed by plugins, they get pushed into a list and invoked in order.
  function updateHandlers(handlerName, fn, options){
    if(!handlers[handlerName]){
      handlers[handlerName] = [];
    }
    handlers[handlerName].push(fn);
    handlerOptions[fn] = options;
    return function(){
      if(!this.settings.optOut) {
        var handlerArgs = Array.prototype.slice.apply(arguments);
        return this.$inject(['$q', angular.bind(this, function($q) {
          return $q.all(handlers[handlerName].map(function(handlerFn) {
            var options = handlerOptions[handlerFn] || {};
            if (options.async) {
              var deferred = $q.defer();
              var currentArgs = angular.copy(handlerArgs);
              currentArgs.unshift(deferred.resolve);
              handlerFn.apply(this, currentArgs);
              return deferred.promise;
            } else{
              return $q.when(handlerFn.apply(this, handlerArgs));
            }
          }, this));
        })]);
      }
    };
  }

  // The api (returned by this provider) gets populated with handlers below.
  var api = {
    settings: settings
  };

  // Opt in and opt out functions
  api.setOptOut = function(optOut) {
    this.settings.optOut = optOut;
    triggerRegister();
  };

  api.getOptOut = function() {
    return this.settings.optOut;
  };


  // Will run setTimeout if delay is > 0
  // Runs immediately if no delay to make sure cache/buffer is flushed before anything else.
  // Plugins should take care to register handlers by order of precedence.
  function onTimeout(fn, delay){
    if(delay){
      setTimeout(fn, delay);
    } else {
      fn();
    }
  }

  var provider = {
    $get: ['$injector', function($injector) {
      return apiWithInjector($injector);
    }],
    api: api,
    settings: settings,
    virtualPageviews: function (value) { this.settings.pageTracking.autoTrackVirtualPages = value; },
    trackStates: function (value) { this.settings.pageTracking.trackStates = value; },
    trackRoutes: function (value) { this.settings.pageTracking.trackRoutes = value; },
    excludeRoutes: function(routes) { this.settings.pageTracking.excludedRoutes = routes; },
    queryKeysWhitelist: function(keys) { this.settings.pageTracking.queryKeysWhitelisted = keys; },
    queryKeysBlacklist: function(keys) { this.settings.pageTracking.queryKeysBlacklisted = keys; },
    filterUrlSegments: function(filters) { this.settings.pageTracking.filterUrlSegments = filters; },
    firstPageview: function (value) { this.settings.pageTracking.autoTrackFirstPage = value; },
    withBase: function (value) {
      this.settings.pageTracking.basePath = (value) ? angular.element(document).find('base').attr('href') : '';
    },
    withAutoBase: function (value) { this.settings.pageTracking.autoBasePath = value; },
    trackExceptions: function (value) { this.settings.trackExceptions = value; },
    developerMode: function(value) { this.settings.developerMode = value; }
  };

  // General function to register plugin handlers. Flushes buffers immediately upon registration according to the specified delay.
  function register(handlerName, fn, options){
    // Do not add a handler if developerMode is true
    if (settings.developerMode) {
        return;
    }
    api[handlerName] = updateHandlers(handlerName, fn, options);
    var handlerSettings = settings[handlerName];
    var handlerDelay = (handlerSettings) ? handlerSettings.bufferFlushDelay : null;
    var delay = (handlerDelay !== null) ? handlerDelay : settings.bufferFlushDelay;
    angular.forEach(cache[handlerName], function (args, index) {
      onTimeout(function () { fn.apply(this, args); }, index * delay);
    });
  }

  function capitalize(input) {
      return input.replace(/^./, function (match) {
          return match.toUpperCase();
      });
  }

  //provide a method to inject services into handlers
  var apiWithInjector = function(injector) {
    return angular.extend(api, {
      '$inject': injector.invoke
    });
  };

  // Adds to the provider a 'register#{handlerName}' function that manages multiple plugins and buffer flushing.
  function installHandlerRegisterFunction(handlerName){
    var registerName = 'register'+capitalize(handlerName);
    provider[registerName] = function(fn, options){
      register(handlerName, fn, options);
    };
    api[handlerName] = updateHandlers(handlerName, bufferedHandler(handlerName));
  }

  function startRegistering(_provider, _knownHandlers, _installHandlerRegisterFunction) {
    angular.forEach(_knownHandlers, _installHandlerRegisterFunction);

    for (var key in _provider) {
      vm[key] = _provider[key];
    }
  }

  // Allow $angulartics to trigger the register to update opt in/out
  var triggerRegister = function() {
    startRegistering(provider, knownHandlers, installHandlerRegisterFunction);
  };

  // Initial register
  startRegistering(provider, knownHandlers, installHandlerRegisterFunction);

}

function $analyticsRun($rootScope, $window, $analytics, $injector) {

  function matchesExcludedRoute(url) {
    for (var i = 0; i < $analytics.settings.pageTracking.excludedRoutes.length; i++) {
      var excludedRoute = $analytics.settings.pageTracking.excludedRoutes[i];
      if ((excludedRoute instanceof RegExp && excludedRoute.test(url)) || url.indexOf(excludedRoute) > -1) {
        return true;
      }
    }
    return false;
  }

  function arrayDifference(a1, a2) {
    var result = [];
    for (var i = 0; i < a1.length; i++) {
      if (a2.indexOf(a1[i]) === -1) {
        result.push(a1[i]);
      }
    }
    return result;
  }

  function filterQueryString(url, keysMatchArr, thisType){
    if (/\?/.test(url) && keysMatchArr.length > 0) {
      var urlArr = url.split('?');
      var urlBase = urlArr[0];
      var pairs = urlArr[1].split('&');
      var matchedPairs = [];

      for (var i = 0; i < keysMatchArr.length; i++) {
        var listedKey = keysMatchArr[i];
        for (var j = 0; j < pairs.length; j++) {
          if ((listedKey instanceof RegExp && listedKey.test(pairs[j])) || pairs[j].indexOf(listedKey) > -1) matchedPairs.push(pairs[j]);
        }
      }

      var matchedPairsArr = (thisType == 'white' ? matchedPairs : arrayDifference(pairs,matchedPairs));
      if(matchedPairsArr.length > 0){
        return urlBase + '?' + matchedPairsArr.join('&');
      }else{
        return urlBase;
      }
    } else {
      return url;
    }
  }

  function whitelistQueryString(url){
    return filterQueryString(url, $analytics.settings.pageTracking.queryKeysWhitelisted, 'white');
  }

  function blacklistQueryString(url){
    return filterQueryString(url, $analytics.settings.pageTracking.queryKeysBlacklisted, 'black');
  }

  function filterUrlSegments(url){
    var segmentFiltersArr = $analytics.settings.pageTracking.filterUrlSegments;

    if (segmentFiltersArr.length > 0) {
      var urlArr = url.split('?');
      var urlBase = urlArr[0];

      var segments = urlBase.split('/');

      for (var i = 0; i < segmentFiltersArr.length; i++) {
        var segmentFilter = segmentFiltersArr[i];

        for (var j = 1; j < segments.length; j++) {
          /* First segment will be host/protocol or base path. */
          if ((segmentFilter instanceof RegExp && segmentFilter.test(segments[j])) || segments[j].indexOf(segmentFilter) > -1) {
            segments[j] = 'FILTERED';
          }
        }
      }

      return segments.join('/');
    } else {
      return url;
    }
  }

  function pageTrack(url, $location) {
    if (!matchesExcludedRoute(url)) {
      url = whitelistQueryString(url);
      url = blacklistQueryString(url);
      url = filterUrlSegments(url);
      $analytics.pageTrack(url, $location);
    }
  }

  if ($analytics.settings.pageTracking.autoTrackFirstPage) {
    /* Only track the 'first page' if there are no routes or states on the page */
    var noRoutesOrStates = true;
    if ($injector.has('$route')) {
       var $route = $injector.get('$route');
       if ($route) {
        for (var route in $route.routes) {
          noRoutesOrStates = false;
          break;
        }
       } else if ($route === null){
        noRoutesOrStates = false;
       }
    } else if ($injector.has('$state')) {
      var $state = $injector.get('$state');
      if ($state.get().length > 1) noRoutesOrStates = false;
    }
    if (noRoutesOrStates) {
      if ($analytics.settings.pageTracking.autoBasePath) {
        $analytics.settings.pageTracking.basePath = $window.location.pathname;
      }
      $injector.invoke(['$location', function ($location) {
        if ($analytics.settings.pageTracking.trackRelativePath) {
          var url = $analytics.settings.pageTracking.basePath + $location.url();
          pageTrack(url, $location);
        } else {
          pageTrack($location.absUrl(), $location);
        }
      }]);
    }
  }

  if ($analytics.settings.pageTracking.autoTrackVirtualPages) {
    if ($analytics.settings.pageTracking.autoBasePath) {
      /* Add the full route to the base. */
      $analytics.settings.pageTracking.basePath = $window.location.pathname + "#";
    }
    var noRoutesOrStates = true;

    if ($analytics.settings.pageTracking.trackRoutes) {
      if ($injector.has('$route')) {
        var $route = $injector.get('$route');
        if ($route) {
          for (var route in $route.routes) {
            noRoutesOrStates = false;
            break;
          }
        } else if ($route === null){
          noRoutesOrStates = false;
        }
        $rootScope.$on('$routeChangeSuccess', function (event, current) {
          if (current && (current.$$route||current).redirectTo) return;
          $injector.invoke(['$location', function ($location) {
            var url = $analytics.settings.pageTracking.basePath + $location.url();
            pageTrack(url, $location);
          }]);
        });
      }
    }

    if ($analytics.settings.pageTracking.trackStates) {
      if ($injector.has('$state') && !$injector.has('$transitions')) {
        noRoutesOrStates = false;
        $rootScope.$on('$stateChangeSuccess', function (event, current) {
          $injector.invoke(['$location', function ($location) {
            var url = $analytics.settings.pageTracking.basePath + $location.url();
            pageTrack(url, $location);
          }]);
        });
      }
      if ($injector.has('$state') && $injector.has('$transitions')) {
        noRoutesOrStates = false;
        $injector.invoke(['$transitions', function($transitions) {
          $transitions.onSuccess({}, function($transition$) {
            var transitionOptions = $transition$.options();

            // only track for transitions that would have triggered $stateChangeSuccess
            if (transitionOptions.notify) {
              $injector.invoke(['$location', function ($location) {
                var url = $analytics.settings.pageTracking.basePath + $location.url();
                pageTrack(url, $location);
              }]);
            }
          });
        }]);
      }
    }

    if (noRoutesOrStates) {
      $rootScope.$on('$locationChangeSuccess', function (event, current) {
        if (current && (current.$$route || current).redirectTo) return;
        $injector.invoke(['$location', function ($location) {
          if ($analytics.settings.pageTracking.trackRelativePath) {
            var url = $analytics.settings.pageTracking.basePath + $location.url();
            pageTrack(url, $location);
          } else {
            pageTrack($location.absUrl(), $location);
          }
        }]);
      });
    }
  }
  if ($analytics.settings.developerMode) {
    angular.forEach($analytics, function(attr, name) {
      if (typeof attr === 'function') {
        $analytics[name] = function(){};
      }
    });
  }
}

function analyticsOn($analytics) {
  return {
    restrict: 'A',
    link: function ($scope, $element, $attrs) {
      var eventType = $attrs.analyticsOn || 'click';
      var trackingData = {};

      angular.forEach($attrs.$attr, function(attr, name) {
        if (isProperty(name)) {
          trackingData[propertyName(name)] = $attrs[name];
          $attrs.$observe(name, function(value){
            trackingData[propertyName(name)] = value;
          });
        }
      });

      angular.element($element[0]).on(eventType, function ($event) {
        var eventName = $attrs.analyticsEvent || inferEventName($element[0]);
        trackingData.eventType = $event.type;

        if($attrs.analyticsIf){
          if(! $scope.$eval($attrs.analyticsIf)){
            return; // Cancel this event if we don't pass the analytics-if condition
          }
        }
        // Allow components to pass through an expression that gets merged on to the event properties
        // eg. analytics-properites='myComponentScope.someConfigExpression.$analyticsProperties'
        if($attrs.analyticsProperties){
          angular.extend(trackingData, $scope.$eval($attrs.analyticsProperties));
        }
        $analytics.eventTrack(eventName, trackingData);
      });
    }
  };
}

function exceptionTrack($provide) {
  $provide.decorator('$exceptionHandler', ['$delegate', '$injector', function ($delegate, $injector) {
    return function (error, cause) {
      var result = $delegate(error, cause);
      var $analytics = $injector.get('$analytics');
      if ($analytics.settings.trackExceptions) {
        $analytics.exceptionTrack(error, cause);
      }
      return result;
    };
  }]);
}

function isCommand(element) {
  return ['a:','button:','button:button','button:submit','input:button','input:submit'].indexOf(
    element.tagName.toLowerCase()+':'+(element.type||'')) >= 0;
}

function inferEventType(element) {
  if (isCommand(element)) return 'click';
  return 'click';
}

function inferEventName(element) {
  if (isCommand(element)) return element.innerText || element.value;
  return element.id || element.name || element.tagName;
}

function isProperty(name) {
  return name.substr(0, 9) === 'analytics' && ['On', 'Event', 'If', 'Properties', 'EventType'].indexOf(name.substr(9)) === -1;
}

function propertyName(name) {
  var s = name.slice(9); // slice off the 'analytics' prefix
  if (typeof s !== 'undefined' && s!==null && s.length > 0) {
    return s.substring(0, 1).toLowerCase() + s.substring(1);
  }
  else {
    return s;
  }
}
})(angular);

/** 
* @version 2.1.10
* @license MIT
*/
!function(t,e){"use strict";t.module("smart-table",[]).run(["$templateCache",function(t){t.put("template/smart-table/pagination.html",'<nav ng-if="numPages && pages.length >= 2"><ul class="pagination"><li ng-repeat="page in pages" ng-class="{active: page==currentPage}"><a href="#" ng-click="selectPage(page); $event.preventDefault(); $event.stopPropagation();">{{page}}</a></li></ul></nav>')}]),t.module("smart-table").constant("stConfig",{pagination:{template:"template/smart-table/pagination.html",itemsByPage:10,displayedPages:5},search:{delay:400,inputEvent:"input",trimSearch:!1},select:{mode:"single",selectedClass:"st-selected"},sort:{ascentClass:"st-sort-ascent",descentClass:"st-sort-descent",descendingFirst:!1,skipNatural:!1,delay:300},pipe:{delay:100}}),t.module("smart-table").controller("stTableController",["$scope","$parse","$filter","$attrs",function(a,n,s,r){function i(t){return t?[].concat(t):[]}function c(){b=i(o(a)),!0===S&&P.pipe()}function l(t,e){if(-1!=e.indexOf(".")){var a=e.split("."),s=a.pop(),r=a.join("."),i=n(r)(t);delete i[s],0==Object.keys(i).length&&l(t,r)}else delete t[e]}var o,u,p,g=r.stTable,d=n(g),f=d.assign,m=s("orderBy"),h=s("filter"),b=i(d(a)),v={sort:{},search:{},pagination:{start:0,totalItemCount:0}},S=!0,P=this;r.stSafeSrc&&(o=n(r.stSafeSrc),a.$watch(function(){var t=o(a);return t&&t.length?t[0]:e},function(t,e){t!==e&&c()}),a.$watch(function(){var t=o(a);return t?t.length:0},function(t,e){t!==b.length&&c()}),a.$watch(function(){return o(a)},function(t,e){t!==e&&(v.pagination.start=0,c())})),this.sortBy=function(e,a){return v.sort.predicate=e,v.sort.reverse=!0===a,t.isFunction(e)?v.sort.functionName=e.name:delete v.sort.functionName,v.pagination.start=0,this.pipe()},this.search=function(t,e,a){var s=v.search.predicateObject||{},r=e||"$";return n(r).assign(s,t),t||l(s,r),v.search.predicateObject=s,v.pagination.start=0,this.pipe()},this.pipe=function(){var t,n=v.pagination;u=v.search.predicateObject?h(b,v.search.predicateObject):b,v.sort.predicate&&(u=m(u,v.sort.predicate,v.sort.reverse)),n.totalItemCount=u.length,n.number!==e&&(n.numberOfPages=u.length>0?Math.ceil(u.length/n.number):1,n.start=n.start>=u.length?(n.numberOfPages-1)*n.number:n.start,t=u.slice(n.start,n.start+parseInt(n.number))),f(a,t||u)},this.select=function(t,n){var s=i(d(a)),r=s.indexOf(t);-1!==r&&("single"===n?(t.isSelected=!0!==t.isSelected,p&&(p.isSelected=!1),p=!0===t.isSelected?t:e):s[r].isSelected=!s[r].isSelected)},this.slice=function(t,e){return v.pagination.start=t,v.pagination.number=e,this.pipe()},this.tableState=function(){return v},this.getFilteredCollection=function(){return u||b},this.setFilterFunction=function(t){h=s(t)},this.setSortFunction=function(t){m=s(t)},this.preventPipeOnWatch=function(){S=!1}}]).directive("stTable",function(){return{restrict:"A",controller:"stTableController",link:function(t,e,a,n){a.stSetFilter&&n.setFilterFunction(a.stSetFilter),a.stSetSort&&n.setSortFunction(a.stSetSort)}}}),t.module("smart-table").directive("stSearch",["stConfig","$timeout","$parse",function(e,a,n){return{require:"^stTable",link:function(s,r,i,c){var l=c,o=null,u=i.stDelay||e.search.delay,p=i.stInputEvent||e.search.inputEvent,g=i.trimSearch||e.search.trimSearch;i.$observe("stSearch",function(e,a){var n=r[0].value;e!==a&&n&&(c.tableState().search={},n=t.isString(n)&&g?n.trim():n,l.search(n,e))}),s.$watch(function(){return c.tableState().search},function(t,e){var a=i.stSearch||"$";t.predicateObject&&n(a)(t.predicateObject)!==r[0].value&&(r[0].value=n(a)(t.predicateObject)||"")},!0),r.bind(p,function(e){e=e.originalEvent||e,null!==o&&a.cancel(o),o=a(function(){var a=e.target.value;a=t.isString(a)&&g?a.trim():a,l.search(a,i.stSearch||""),o=null},u)})}}}]),t.module("smart-table").directive("stSelectRow",["stConfig",function(t){return{restrict:"A",require:"^stTable",scope:{row:"=stSelectRow"},link:function(e,a,n,s){var r=n.stSelectMode||t.select.mode;a.bind("click",function(){e.$apply(function(){s.select(e.row,r)})}),e.$watch("row.isSelected",function(e){!0===e?a.addClass(t.select.selectedClass):a.removeClass(t.select.selectedClass)})}}}]),t.module("smart-table").directive("stSort",["stConfig","$parse","$timeout",function(a,n,s){return{restrict:"A",require:"^stTable",link:function(r,i,c,l){function o(){v?d=0===d?2:d-1:d++;var e;p=t.isFunction(g(r))||t.isArray(g(r))?g(r):c.stSort,d%3==0&&!0!=!!b?(d=0,l.tableState().sort={},l.tableState().pagination.start=0,e=l.pipe.bind(l)):e=l.sortBy.bind(l,p,d%2==0),null!==S&&s.cancel(S),P<0?e():S=s(function(){e()},P)}var u,p=c.stSort,g=n(p),d=0,f=c.stClassAscent||a.sort.ascentClass,m=c.stClassDescent||a.sort.descentClass,h=[f,m],b=c.stSkipNatural!==e?c.stSkipNatural:a.sort.skipNatural,v=c.stDescendingFirst!==e?c.stDescendingFirst:a.sort.descendingFirst,S=null,P=c.stDelay||a.sort.delay,y="aria-sort";i.attr("role","columnheader").attr(y,"none"),c.stSortDefault&&(u=r.$eval(c.stSortDefault)!==e?r.$eval(c.stSortDefault):c.stSortDefault),i.bind("click",function(){p&&r.$apply(o)}),u&&(d="reverse"===u?1:0,o()),r.$watch(function(){return l.tableState().sort},function(t){t.predicate!==p?(d=0,i.removeClass(f).removeClass(m).attr(y,"none")):(d=!0===t.reverse?2:1,i.removeClass(h[d%2]).addClass(h[d-1]).attr(y,t.reverse?"ascending":"descending"))},!0)}}}]),t.module("smart-table").directive("stPagination",["stConfig",function(t){return{restrict:"EA",require:"^stTable",scope:{stItemsByPage:"=?",stDisplayedPages:"=?",stPageChange:"&"},templateUrl:function(e,a){return a.stTemplate?a.stTemplate:t.pagination.template},link:function(e,a,n,s){function r(){var t,a,n=s.tableState().pagination,r=1,i=e.currentPage;for(e.totalItemCount=n.totalItemCount,e.currentPage=Math.floor(n.start/n.number)+1,(t=(r=Math.max(r,e.currentPage-Math.abs(Math.floor(e.stDisplayedPages/2))))+e.stDisplayedPages)>n.numberOfPages&&(t=n.numberOfPages+1,r=Math.max(1,t-e.stDisplayedPages)),e.pages=[],e.numPages=n.numberOfPages,a=r;a<t;a++)e.pages.push(a);i!==e.currentPage&&e.stPageChange({newPage:e.currentPage})}e.stItemsByPage=e.stItemsByPage?+e.stItemsByPage:t.pagination.itemsByPage,e.stDisplayedPages=e.stDisplayedPages?+e.stDisplayedPages:t.pagination.displayedPages,e.currentPage=1,e.pages=[],e.$watch(function(){return s.tableState().pagination},r,!0),e.$watch("stItemsByPage",function(t,a){t!==a&&e.selectPage(1)}),e.$watch("stDisplayedPages",r),e.selectPage=function(t){t>0&&t<=e.numPages&&s.slice((t-1)*e.stItemsByPage,e.stItemsByPage)},s.tableState().pagination.number||s.slice(0,e.stItemsByPage)}}}]),t.module("smart-table").directive("stPipe",["stConfig","$timeout",function(e,a){return{require:"stTable",scope:{stPipe:"="},link:{pre:function(n,s,r,i){var c=null;t.isFunction(n.stPipe)&&(i.preventPipeOnWatch(),i.pipe=function(){return null!==c&&a.cancel(c),c=a(function(){n.stPipe(i.tableState(),i)},e.pipe.delay)})},post:function(t,e,a,n){n.pipe()}}}}])}(angular);
//# sourceMappingURL=smart-table.min.js.map

(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else {
		var a = factory();
		for(var i in a) (typeof exports === 'object' ? exports : root)[i] = a[i];
	}
})(this, function() {
return /******/ (function(modules) { // webpackBootstrap
/******/ 	// The module cache
/******/ 	var installedModules = {};

/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {

/******/ 		// Check if module is in cache
/******/ 		if(installedModules[moduleId])
/******/ 			return installedModules[moduleId].exports;

/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = installedModules[moduleId] = {
/******/ 			exports: {},
/******/ 			id: moduleId,
/******/ 			loaded: false
/******/ 		};

/******/ 		// Execute the module function
/******/ 		modules[moduleId].call(module.exports, module, module.exports, __webpack_require__);

/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;

/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}


/******/ 	// expose the modules object (__webpack_modules__)
/******/ 	__webpack_require__.m = modules;

/******/ 	// expose the module cache
/******/ 	__webpack_require__.c = installedModules;

/******/ 	// __webpack_public_path__
/******/ 	__webpack_require__.p = "";

/******/ 	// Load entry module and return exports
/******/ 	return __webpack_require__(0);
/******/ })
/************************************************************************/
/******/ ([
/* 0 */
/***/ function(module, exports, __webpack_require__) {

	'use strict';

	/*
	*
	* A AngularJS module that implements the HTML5 W3C saveAs() in browsers that
	* do not natively support it
	*
	* (c) 2015 Philipp Alferov
	* License: MIT
	*
	*/

	module.exports = 'ngFileSaver';

	angular.module('ngFileSaver', [])
	  .factory('FileSaver', ['Blob', 'SaveAs', 'FileSaverUtils', __webpack_require__(1)])
	  .factory('FileSaverUtils', [__webpack_require__(2)])
	  .factory('Blob', ['$window', 'FileSaverUtils', __webpack_require__(3)])
	  .factory('SaveAs', ['$window', 'FileSaverUtils', __webpack_require__(4)]);


/***/ },
/* 1 */
/***/ function(module, exports) {

	'use strict';

	module.exports = function FileSaver(Blob, SaveAs, FileSaverUtils) {

	  function save(blob, filename, disableAutoBOM) {
	    try {
	      SaveAs(blob, filename, disableAutoBOM);
	    } catch(err) {
	      FileSaverUtils.handleErrors(err.message);
	    }
	  }

	  return {

	    /**
	    * saveAs
	    * Immediately starts saving a file, returns undefined.
	    *
	    * @name saveAs
	    * @function
	    * @param {Blob} data A Blob instance
	    * @param {Object} filename Custom filename (extension is optional)
	    * @param {Boolean} disableAutoBOM Disable automatically provided Unicode
	    * text encoding hints
	    *
	    * @return {Undefined}
	    */

	    saveAs: function(data, filename, disableAutoBOM) {

	      if (!FileSaverUtils.isBlobInstance(data)) {
	        FileSaverUtils.handleErrors('Data argument should be a blob instance');
	      }

	      if (!FileSaverUtils.isString(filename)) {
	        FileSaverUtils.handleErrors('Filename argument should be a string');
	      }

	      return save(data, filename, disableAutoBOM);
	    }
	  };
	};


/***/ },
/* 2 */
/***/ function(module, exports) {

	'use strict';

	module.exports = function FileSaverUtils() {
	  return {
	    handleErrors: function(msg) {
	      throw new Error(msg);
	    },
	    isString: function(obj) {
	      return typeof obj === 'string' || obj instanceof String;
	    },
	    isUndefined: function(obj) {
	      return typeof obj === 'undefined';
	    },
	    isBlobInstance: function(obj) {
	      return obj instanceof Blob;
	    }
	  };
	};


/***/ },
/* 3 */
/***/ function(module, exports) {

	'use strict';

	module.exports = function Blob($window, FileSaverUtils) {
	  var blob = $window.Blob;

	  if (FileSaverUtils.isUndefined(blob)) {
	    FileSaverUtils.handleErrors('Blob is not supported. Please include blob polyfilll');
	  }

	  return blob;
	};


/***/ },
/* 4 */
/***/ function(module, exports) {

	'use strict';

	module.exports = function SaveAs($window, FileSaverUtils) {
	  var saveAs = $window.saveAs;

	  if (FileSaverUtils.isUndefined(saveAs)) {
	    FileSaverUtils.handleErrors('saveAs is not supported. Please include saveAs polyfill');
	  }

	  return saveAs;
	};


/***/ }
/******/ ])
});
;
(function (global, factory) {
  if (typeof define === "function" && define.amd) {
    define([], factory);
  } else if (typeof exports !== "undefined") {
    factory();
  } else {
    var mod = {
      exports: {}
    };
    factory();
    global.FileSaver = mod.exports;
  }
})(this, function () {
  "use strict";

  /*
  * FileSaver.js
  * A saveAs() FileSaver implementation.
  *
  * By Eli Grey, http://eligrey.com
  *
  * License : https://github.com/eligrey/FileSaver.js/blob/master/LICENSE.md (MIT)
  * source  : http://purl.eligrey.com/github/FileSaver.js
  */
  // The one and only way of getting global scope in all environments
  // https://stackoverflow.com/q/3277182/1008999
  var _global = typeof window === 'object' && window.window === window ? window : typeof self === 'object' && self.self === self ? self : typeof global === 'object' && global.global === global ? global : void 0;

  function bom(blob, opts) {
    if (typeof opts === 'undefined') opts = {
      autoBom: false
    };else if (typeof opts !== 'object') {
      console.warn('Deprecated: Expected third argument to be a object');
      opts = {
        autoBom: !opts
      };
    } // prepend BOM for UTF-8 XML and text/* types (including HTML)
    // note: your browser will automatically convert UTF-16 U+FEFF to EF BB BF

    if (opts.autoBom && /^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
      return new Blob([String.fromCharCode(0xFEFF), blob], {
        type: blob.type
      });
    }

    return blob;
  }

  function download(url, name, opts) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob';

    xhr.onload = function () {
      saveAs(xhr.response, name, opts);
    };

    xhr.onerror = function () {
      console.error('could not download file');
    };

    xhr.send();
  }

  function corsEnabled(url) {
    var xhr = new XMLHttpRequest(); // use sync to avoid popup blocker

    xhr.open('HEAD', url, false);

    try {
      xhr.send();
    } catch (e) {}

    return xhr.status >= 200 && xhr.status <= 299;
  } // `a.click()` doesn't work for all browsers (#465)


  function click(node) {
    try {
      node.dispatchEvent(new MouseEvent('click'));
    } catch (e) {
      var evt = document.createEvent('MouseEvents');
      evt.initMouseEvent('click', true, true, window, 0, 0, 0, 80, 20, false, false, false, false, 0, null);
      node.dispatchEvent(evt);
    }
  } // Detect WebView inside a native macOS app by ruling out all browsers
  // We just need to check for 'Safari' because all other browsers (besides Firefox) include that too
  // https://www.whatismybrowser.com/guides/the-latest-user-agent/macos


  var isMacOSWebView = /Macintosh/.test(navigator.userAgent) && /AppleWebKit/.test(navigator.userAgent) && !/Safari/.test(navigator.userAgent);
  var saveAs = _global.saveAs || ( // probably in some web worker
  typeof window !== 'object' || window !== _global ? function saveAs() {}
  /* noop */
  // Use download attribute first if possible (#193 Lumia mobile) unless this is a macOS WebView
  : 'download' in HTMLAnchorElement.prototype && !isMacOSWebView ? function saveAs(blob, name, opts) {
    var URL = _global.URL || _global.webkitURL;
    var a = document.createElement('a');
    name = name || blob.name || 'download';
    a.download = name;
    a.rel = 'noopener'; // tabnabbing
    // TODO: detect chrome extensions & packaged apps
    // a.target = '_blank'

    if (typeof blob === 'string') {
      // Support regular links
      a.href = blob;

      if (a.origin !== location.origin) {
        corsEnabled(a.href) ? download(blob, name, opts) : click(a, a.target = '_blank');
      } else {
        click(a);
      }
    } else {
      // Support blobs
      a.href = URL.createObjectURL(blob);
      setTimeout(function () {
        URL.revokeObjectURL(a.href);
      }, 4E4); // 40s

      setTimeout(function () {
        click(a);
      }, 0);
    }
  } // Use msSaveOrOpenBlob as a second approach
  : 'msSaveOrOpenBlob' in navigator ? function saveAs(blob, name, opts) {
    name = name || blob.name || 'download';

    if (typeof blob === 'string') {
      if (corsEnabled(blob)) {
        download(blob, name, opts);
      } else {
        var a = document.createElement('a');
        a.href = blob;
        a.target = '_blank';
        setTimeout(function () {
          click(a);
        });
      }
    } else {
      navigator.msSaveOrOpenBlob(bom(blob, opts), name);
    }
  } // Fallback to using FileReader and a popup
  : function saveAs(blob, name, opts, popup) {
    // Open a popup immediately do go around popup blocker
    // Mostly only available on user interaction and the fileReader is async so...
    popup = popup || open('', '_blank');

    if (popup) {
      popup.document.title = popup.document.body.innerText = 'downloading...';
    }

    if (typeof blob === 'string') return download(blob, name, opts);
    var force = blob.type === 'application/octet-stream';

    var isSafari = /constructor/i.test(_global.HTMLElement) || _global.safari;

    var isChromeIOS = /CriOS\/[\d]+/.test(navigator.userAgent);

    if ((isChromeIOS || force && isSafari || isMacOSWebView) && typeof FileReader !== 'undefined') {
      // Safari doesn't allow downloading of blob URLs
      var reader = new FileReader();

      reader.onloadend = function () {
        var url = reader.result;
        url = isChromeIOS ? url : url.replace(/^data:[^;]*;/, 'data:attachment/file;');
        if (popup) popup.location.href = url;else location = url;
        popup = null; // reverse-tabnabbing #460
      };

      reader.readAsDataURL(blob);
    } else {
      var URL = _global.URL || _global.webkitURL;
      var url = URL.createObjectURL(blob);
      if (popup) popup.location = url;else location.href = url;
      popup = null; // reverse-tabnabbing #460

      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 4E4); // 40s
    }
  });
  _global.saveAs = saveAs.saveAs = saveAs;

  if (typeof module !== 'undefined') {
    module.exports = saveAs;
  }
});

!function a(b,c,d){function e(g,h){if(!c[g]){if(!b[g]){var i="function"==typeof require&&require;if(!h&&i)return i(g,!0);if(f)return f(g,!0);var j=new Error("Cannot find module '"+g+"'");throw j.code="MODULE_NOT_FOUND",j}var k=c[g]={exports:{}};b[g][0].call(k.exports,function(a){var c=b[g][1][a];return e(c?c:a)},k,k.exports,a,b,c,d)}return c[g].exports}for(var f="function"==typeof require&&require,g=0;g<d.length;g++)e(d[g]);return e}({1:[function(a,b,c){function d(a){this.message=a}function e(a){var b=String(a).replace(/=+$/,"");if(b.length%4==1)throw new d("'atob' failed: The string to be decoded is not correctly encoded.");for(var c,e,g=0,h=0,i="";e=b.charAt(h++);~e&&(c=g%4?64*c+e:e,g++%4)?i+=String.fromCharCode(255&c>>(-2*g&6)):0)e=f.indexOf(e);return i}var f="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";d.prototype=new Error,d.prototype.name="InvalidCharacterError",b.exports="undefined"!=typeof window&&window.atob&&window.atob.bind(window)||e},{}],2:[function(a,b,c){function d(a){return decodeURIComponent(e(a).replace(/(.)/g,function(a,b){var c=b.charCodeAt(0).toString(16).toUpperCase();return c.length<2&&(c="0"+c),"%"+c}))}var e=a("./atob");b.exports=function(a){var b=a.replace(/-/g,"+").replace(/_/g,"/");switch(b.length%4){case 0:break;case 2:b+="==";break;case 3:b+="=";break;default:throw"Illegal base64url string!"}try{return d(b)}catch(c){return e(b)}}},{"./atob":1}],3:[function(a,b,c){"use strict";function d(a){this.message=a}var e=a("./base64_url_decode");d.prototype=new Error,d.prototype.name="InvalidTokenError",b.exports=function(a,b){if("string"!=typeof a)throw new d("Invalid token specified");b=b||{};var c=b.header===!0?0:1;try{return JSON.parse(e(a.split(".")[c]))}catch(f){throw new d("Invalid token specified: "+f.message)}},b.exports.InvalidTokenError=d},{"./base64_url_decode":2}],4:[function(a,b,c){(function(b){var c=a("./lib/index");"function"==typeof b.window.define&&b.window.define.amd?b.window.define("jwt_decode",function(){return c}):b.window&&(b.window.jwt_decode=c)}).call(this,"undefined"!=typeof global?global:"undefined"!=typeof self?self:"undefined"!=typeof window?window:{})},{"./lib/index":3}]},{},[4]);
/**!
 * AngularJS file upload directives and services. Supoorts: file upload/drop/paste, resume, cancel/abort,
 * progress, resize, thumbnail, preview, validation and CORS
 * @author  Danial  <danial.farid@gmail.com>
 * @version 12.2.13
 */

if (window.XMLHttpRequest && !(window.FileAPI && FileAPI.shouldLoad)) {
  window.XMLHttpRequest.prototype.setRequestHeader = (function (orig) {
    return function (header, value) {
      if (header === '__setXHR_') {
        var val = value(this);
        // fix for angular < 1.2.0
        if (val instanceof Function) {
          val(this);
        }
      } else {
        orig.apply(this, arguments);
      }
    };
  })(window.XMLHttpRequest.prototype.setRequestHeader);
}

var ngFileUpload = angular.module('ngFileUpload', []);

ngFileUpload.version = '12.2.13';

ngFileUpload.service('UploadBase', ['$http', '$q', '$timeout', function ($http, $q, $timeout) {
  var upload = this;
  upload.promisesCount = 0;

  this.isResumeSupported = function () {
    return window.Blob && window.Blob.prototype.slice;
  };

  var resumeSupported = this.isResumeSupported();

  function sendHttp(config) {
    config.method = config.method || 'POST';
    config.headers = config.headers || {};

    var deferred = config._deferred = config._deferred || $q.defer();
    var promise = deferred.promise;

    function notifyProgress(e) {
      if (deferred.notify) {
        deferred.notify(e);
      }
      if (promise.progressFunc) {
        $timeout(function () {
          promise.progressFunc(e);
        });
      }
    }

    function getNotifyEvent(n) {
      if (config._start != null && resumeSupported) {
        return {
          loaded: n.loaded + config._start,
          total: (config._file && config._file.size) || n.total,
          type: n.type, config: config,
          lengthComputable: true, target: n.target
        };
      } else {
        return n;
      }
    }

    if (!config.disableProgress) {
      config.headers.__setXHR_ = function () {
        return function (xhr) {
          if (!xhr || !xhr.upload || !xhr.upload.addEventListener) return;
          config.__XHR = xhr;
          if (config.xhrFn) config.xhrFn(xhr);
          xhr.upload.addEventListener('progress', function (e) {
            e.config = config;
            notifyProgress(getNotifyEvent(e));
          }, false);
          //fix for firefox not firing upload progress end, also IE8-9
          xhr.upload.addEventListener('load', function (e) {
            if (e.lengthComputable) {
              e.config = config;
              notifyProgress(getNotifyEvent(e));
            }
          }, false);
        };
      };
    }

    function uploadWithAngular() {
      $http(config).then(function (r) {
          if (resumeSupported && config._chunkSize && !config._finished && config._file) {
            var fileSize = config._file && config._file.size || 0;
            notifyProgress({
                loaded: Math.min(config._end, fileSize),
                total: fileSize,
                config: config,
                type: 'progress'
              }
            );
            upload.upload(config, true);
          } else {
            if (config._finished) delete config._finished;
            deferred.resolve(r);
          }
        }, function (e) {
          deferred.reject(e);
        }, function (n) {
          deferred.notify(n);
        }
      );
    }

    if (!resumeSupported) {
      uploadWithAngular();
    } else if (config._chunkSize && config._end && !config._finished) {
      config._start = config._end;
      config._end += config._chunkSize;
      uploadWithAngular();
    } else if (config.resumeSizeUrl) {
      $http.get(config.resumeSizeUrl).then(function (resp) {
        if (config.resumeSizeResponseReader) {
          config._start = config.resumeSizeResponseReader(resp.data);
        } else {
          config._start = parseInt((resp.data.size == null ? resp.data : resp.data.size).toString());
        }
        if (config._chunkSize) {
          config._end = config._start + config._chunkSize;
        }
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else if (config.resumeSize) {
      config.resumeSize().then(function (size) {
        config._start = size;
        if (config._chunkSize) {
          config._end = config._start + config._chunkSize;
        }
        uploadWithAngular();
      }, function (e) {
        throw e;
      });
    } else {
      if (config._chunkSize) {
        config._start = 0;
        config._end = config._start + config._chunkSize;
      }
      uploadWithAngular();
    }


    promise.success = function (fn) {
      promise.then(function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.error = function (fn) {
      promise.then(null, function (response) {
        fn(response.data, response.status, response.headers, config);
      });
      return promise;
    };

    promise.progress = function (fn) {
      promise.progressFunc = fn;
      promise.then(null, null, function (n) {
        fn(n);
      });
      return promise;
    };
    promise.abort = promise.pause = function () {
      if (config.__XHR) {
        $timeout(function () {
          config.__XHR.abort();
        });
      }
      return promise;
    };
    promise.xhr = function (fn) {
      config.xhrFn = (function (origXhrFn) {
        return function () {
          if (origXhrFn) origXhrFn.apply(promise, arguments);
          fn.apply(promise, arguments);
        };
      })(config.xhrFn);
      return promise;
    };

    upload.promisesCount++;
    if (promise['finally'] && promise['finally'] instanceof Function) {
      promise['finally'](function () {
        upload.promisesCount--;
      });
    }
    return promise;
  }

  this.isUploadInProgress = function () {
    return upload.promisesCount > 0;
  };

  this.rename = function (file, name) {
    file.ngfName = name;
    return file;
  };

  this.jsonBlob = function (val) {
    if (val != null && !angular.isString(val)) {
      val = JSON.stringify(val);
    }
    var blob = new window.Blob([val], {type: 'application/json'});
    blob._ngfBlob = true;
    return blob;
  };

  this.json = function (val) {
    return angular.toJson(val);
  };

  function copy(obj) {
    var clone = {};
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        clone[key] = obj[key];
      }
    }
    return clone;
  }

  this.isFile = function (file) {
    return file != null && (file instanceof window.Blob || (file.flashId && file.name && file.size));
  };

  this.upload = function (config, internal) {
    function toResumeFile(file, formData) {
      if (file._ngfBlob) return file;
      config._file = config._file || file;
      if (config._start != null && resumeSupported) {
        if (config._end && config._end >= file.size) {
          config._finished = true;
          config._end = file.size;
        }
        var slice = file.slice(config._start, config._end || file.size);
        slice.name = file.name;
        slice.ngfName = file.ngfName;
        if (config._chunkSize) {
          formData.append('_chunkSize', config._chunkSize);
          formData.append('_currentChunkSize', config._end - config._start);
          formData.append('_chunkNumber', Math.floor(config._start / config._chunkSize));
          formData.append('_totalSize', config._file.size);
        }
        return slice;
      }
      return file;
    }

    function addFieldToFormData(formData, val, key) {
      if (val !== undefined) {
        if (angular.isDate(val)) {
          val = val.toISOString();
        }
        if (angular.isString(val)) {
          formData.append(key, val);
        } else if (upload.isFile(val)) {
          var file = toResumeFile(val, formData);
          var split = key.split(',');
          if (split[1]) {
            file.ngfName = split[1].replace(/^\s+|\s+$/g, '');
            key = split[0];
          }
          config._fileKey = config._fileKey || key;
          formData.append(key, file, file.ngfName || file.name);
        } else {
          if (angular.isObject(val)) {
            if (val.$$ngfCircularDetection) throw 'ngFileUpload: Circular reference in config.data. Make sure specified data for Upload.upload() has no circular reference: ' + key;

            val.$$ngfCircularDetection = true;
            try {
              for (var k in val) {
                if (val.hasOwnProperty(k) && k !== '$$ngfCircularDetection') {
                  var objectKey = config.objectKey == null ? '[i]' : config.objectKey;
                  if (val.length && parseInt(k) > -1) {
                    objectKey = config.arrayKey == null ? objectKey : config.arrayKey;
                  }
                  addFieldToFormData(formData, val[k], key + objectKey.replace(/[ik]/g, k));
                }
              }
            } finally {
              delete val.$$ngfCircularDetection;
            }
          } else {
            formData.append(key, val);
          }
        }
      }
    }

    function digestConfig() {
      config._chunkSize = upload.translateScalars(config.resumeChunkSize);
      config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

      config.headers = config.headers || {};
      config.headers['Content-Type'] = undefined;
      config.transformRequest = config.transformRequest ?
        (angular.isArray(config.transformRequest) ?
          config.transformRequest : [config.transformRequest]) : [];
      config.transformRequest.push(function (data) {
        var formData = new window.FormData(), key;
        data = data || config.fields || {};
        if (config.file) {
          data.file = config.file;
        }
        for (key in data) {
          if (data.hasOwnProperty(key)) {
            var val = data[key];
            if (config.formDataAppender) {
              config.formDataAppender(formData, key, val);
            } else {
              addFieldToFormData(formData, val, key);
            }
          }
        }

        return formData;
      });
    }

    if (!internal) config = copy(config);
    if (!config._isDigested) {
      config._isDigested = true;
      digestConfig();
    }

    return sendHttp(config);
  };

  this.http = function (config) {
    config = copy(config);
    config.transformRequest = config.transformRequest || function (data) {
        if ((window.ArrayBuffer && data instanceof window.ArrayBuffer) || data instanceof window.Blob) {
          return data;
        }
        return $http.defaults.transformRequest[0].apply(this, arguments);
      };
    config._chunkSize = upload.translateScalars(config.resumeChunkSize);
    config._chunkSize = config._chunkSize ? parseInt(config._chunkSize.toString()) : null;

    return sendHttp(config);
  };

  this.translateScalars = function (str) {
    if (angular.isString(str)) {
      if (str.search(/kb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1024);
      } else if (str.search(/mb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1048576);
      } else if (str.search(/gb/i) === str.length - 2) {
        return parseFloat(str.substring(0, str.length - 2) * 1073741824);
      } else if (str.search(/b/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/s/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1));
      } else if (str.search(/m/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 60);
      } else if (str.search(/h/i) === str.length - 1) {
        return parseFloat(str.substring(0, str.length - 1) * 3600);
      }
    }
    return str;
  };

  this.urlToBlob = function(url) {
    var defer = $q.defer();
    $http({url: url, method: 'get', responseType: 'arraybuffer'}).then(function (resp) {
      var arrayBufferView = new Uint8Array(resp.data);
      var type = resp.headers('content-type') || 'image/WebP';
      var blob = new window.Blob([arrayBufferView], {type: type});
      var matches = url.match(/.*\/(.+?)(\?.*)?$/);
      if (matches.length > 1) {
        blob.name = matches[1];
      }
      defer.resolve(blob);
    }, function (e) {
      defer.reject(e);
    });
    return defer.promise;
  };

  this.setDefaults = function (defaults) {
    this.defaults = defaults || {};
  };

  this.defaults = {};
  this.version = ngFileUpload.version;
}

]);

ngFileUpload.service('Upload', ['$parse', '$timeout', '$compile', '$q', 'UploadExif', function ($parse, $timeout, $compile, $q, UploadExif) {
  var upload = UploadExif;
  upload.getAttrWithDefaults = function (attr, name) {
    if (attr[name] != null) return attr[name];
    var def = upload.defaults[name];
    return (def == null ? def : (angular.isString(def) ? def : JSON.stringify(def)));
  };

  upload.attrGetter = function (name, attr, scope, params) {
    var attrVal = this.getAttrWithDefaults(attr, name);
    if (scope) {
      try {
        if (params) {
          return $parse(attrVal)(scope, params);
        } else {
          return $parse(attrVal)(scope);
        }
      } catch (e) {
        // hangle string value without single qoute
        if (name.search(/min|max|pattern/i)) {
          return attrVal;
        } else {
          throw e;
        }
      }
    } else {
      return attrVal;
    }
  };

  upload.shouldUpdateOn = function (type, attr, scope) {
    var modelOptions = upload.attrGetter('ngfModelOptions', attr, scope);
    if (modelOptions && modelOptions.updateOn) {
      return modelOptions.updateOn.split(' ').indexOf(type) > -1;
    }
    return true;
  };

  upload.emptyPromise = function () {
    var d = $q.defer();
    var args = arguments;
    $timeout(function () {
      d.resolve.apply(d, args);
    });
    return d.promise;
  };

  upload.rejectPromise = function () {
    var d = $q.defer();
    var args = arguments;
    $timeout(function () {
      d.reject.apply(d, args);
    });
    return d.promise;
  };

  upload.happyPromise = function (promise, data) {
    var d = $q.defer();
    promise.then(function (result) {
      d.resolve(result);
    }, function (error) {
      $timeout(function () {
        throw error;
      });
      d.resolve(data);
    });
    return d.promise;
  };

  function applyExifRotations(files, attr, scope) {
    var promises = [upload.emptyPromise()];
    angular.forEach(files, function (f, i) {
      if (f.type.indexOf('image/jpeg') === 0 && upload.attrGetter('ngfFixOrientation', attr, scope, {$file: f})) {
        promises.push(upload.happyPromise(upload.applyExifRotation(f), f).then(function (fixedFile) {
          files.splice(i, 1, fixedFile);
        }));
      }
    });
    return $q.all(promises);
  }

  function resizeFile(files, attr, scope, ngModel) {
    var resizeVal = upload.attrGetter('ngfResize', attr, scope);
    if (!resizeVal || !upload.isResizeSupported() || !files.length) return upload.emptyPromise();
    if (resizeVal instanceof Function) {
      var defer = $q.defer();
      return resizeVal(files).then(function (p) {
        resizeWithParams(p, files, attr, scope, ngModel).then(function (r) {
          defer.resolve(r);
        }, function (e) {
          defer.reject(e);
        });
      }, function (e) {
        defer.reject(e);
      });
    } else {
      return resizeWithParams(resizeVal, files, attr, scope, ngModel);
    }
  }

  function resizeWithParams(params, files, attr, scope, ngModel) {
    var promises = [upload.emptyPromise()];

    function handleFile(f, i) {
      if (f.type.indexOf('image') === 0) {
        if (params.pattern && !upload.validatePattern(f, params.pattern)) return;
        params.resizeIf = function (width, height) {
          return upload.attrGetter('ngfResizeIf', attr, scope,
            {$width: width, $height: height, $file: f});
        };
        var promise = upload.resize(f, params);
        promises.push(promise);
        promise.then(function (resizedFile) {
          files.splice(i, 1, resizedFile);
        }, function (e) {
          f.$error = 'resize';
          (f.$errorMessages = (f.$errorMessages || {})).resize = true;
          f.$errorParam = (e ? (e.message ? e.message : e) + ': ' : '') + (f && f.name);
          ngModel.$ngfValidations.push({name: 'resize', valid: false});
          upload.applyModelValidation(ngModel, files);
        });
      }
    }

    for (var i = 0; i < files.length; i++) {
      handleFile(files[i], i);
    }
    return $q.all(promises);
  }

  upload.updateModel = function (ngModel, attr, scope, fileChange, files, evt, noDelay) {
    function update(files, invalidFiles, newFiles, dupFiles, isSingleModel) {
      attr.$$ngfPrevValidFiles = files;
      attr.$$ngfPrevInvalidFiles = invalidFiles;
      var file = files && files.length ? files[0] : null;
      var invalidFile = invalidFiles && invalidFiles.length ? invalidFiles[0] : null;

      if (ngModel) {
        upload.applyModelValidation(ngModel, files);
        ngModel.$setViewValue(isSingleModel ? file : files);
      }

      if (fileChange) {
        $parse(fileChange)(scope, {
          $files: files,
          $file: file,
          $newFiles: newFiles,
          $duplicateFiles: dupFiles,
          $invalidFiles: invalidFiles,
          $invalidFile: invalidFile,
          $event: evt
        });
      }

      var invalidModel = upload.attrGetter('ngfModelInvalid', attr);
      if (invalidModel) {
        $timeout(function () {
          $parse(invalidModel).assign(scope, isSingleModel ? invalidFile : invalidFiles);
        });
      }
      $timeout(function () {
        // scope apply changes
      });
    }

    var allNewFiles, dupFiles = [], prevValidFiles, prevInvalidFiles,
      invalids = [], valids = [];

    function removeDuplicates() {
      function equals(f1, f2) {
        return f1.name === f2.name && (f1.$ngfOrigSize || f1.size) === (f2.$ngfOrigSize || f2.size) &&
          f1.type === f2.type;
      }

      function isInPrevFiles(f) {
        var j;
        for (j = 0; j < prevValidFiles.length; j++) {
          if (equals(f, prevValidFiles[j])) {
            return true;
          }
        }
        for (j = 0; j < prevInvalidFiles.length; j++) {
          if (equals(f, prevInvalidFiles[j])) {
            return true;
          }
        }
        return false;
      }

      if (files) {
        allNewFiles = [];
        dupFiles = [];
        for (var i = 0; i < files.length; i++) {
          if (isInPrevFiles(files[i])) {
            dupFiles.push(files[i]);
          } else {
            allNewFiles.push(files[i]);
          }
        }
      }
    }

    function toArray(v) {
      return angular.isArray(v) ? v : [v];
    }

    function resizeAndUpdate() {
      function updateModel() {
        $timeout(function () {
          update(keep ? prevValidFiles.concat(valids) : valids,
            keep ? prevInvalidFiles.concat(invalids) : invalids,
            files, dupFiles, isSingleModel);
        }, options && options.debounce ? options.debounce.change || options.debounce : 0);
      }

      var resizingFiles = validateAfterResize ? allNewFiles : valids;
      resizeFile(resizingFiles, attr, scope, ngModel).then(function () {
        if (validateAfterResize) {
          upload.validate(allNewFiles, keep ? prevValidFiles.length : 0, ngModel, attr, scope)
            .then(function (validationResult) {
              valids = validationResult.validsFiles;
              invalids = validationResult.invalidsFiles;
              updateModel();
            });
        } else {
          updateModel();
        }
      }, function () {
        for (var i = 0; i < resizingFiles.length; i++) {
          var f = resizingFiles[i];
          if (f.$error === 'resize') {
            var index = valids.indexOf(f);
            if (index > -1) {
              valids.splice(index, 1);
              invalids.push(f);
            }
            updateModel();
          }
        }
      });
    }

    prevValidFiles = attr.$$ngfPrevValidFiles || [];
    prevInvalidFiles = attr.$$ngfPrevInvalidFiles || [];
    if (ngModel && ngModel.$modelValue) {
      prevValidFiles = toArray(ngModel.$modelValue);
    }

    var keep = upload.attrGetter('ngfKeep', attr, scope);
    allNewFiles = (files || []).slice(0);
    if (keep === 'distinct' || upload.attrGetter('ngfKeepDistinct', attr, scope) === true) {
      removeDuplicates(attr, scope);
    }

    var isSingleModel = !keep && !upload.attrGetter('ngfMultiple', attr, scope) && !upload.attrGetter('multiple', attr);

    if (keep && !allNewFiles.length) return;

    upload.attrGetter('ngfBeforeModelChange', attr, scope, {
      $files: files,
      $file: files && files.length ? files[0] : null,
      $newFiles: allNewFiles,
      $duplicateFiles: dupFiles,
      $event: evt
    });

    var validateAfterResize = upload.attrGetter('ngfValidateAfterResize', attr, scope);

    var options = upload.attrGetter('ngfModelOptions', attr, scope);
    upload.validate(allNewFiles, keep ? prevValidFiles.length : 0, ngModel, attr, scope)
      .then(function (validationResult) {
      if (noDelay) {
        update(allNewFiles, [], files, dupFiles, isSingleModel);
      } else {
        if ((!options || !options.allowInvalid) && !validateAfterResize) {
          valids = validationResult.validFiles;
          invalids = validationResult.invalidFiles;
        } else {
          valids = allNewFiles;
        }
        if (upload.attrGetter('ngfFixOrientation', attr, scope) && upload.isExifSupported()) {
          applyExifRotations(valids, attr, scope).then(function () {
            resizeAndUpdate();
          });
        } else {
          resizeAndUpdate();
        }
      }
    });
  };

  return upload;
}]);

ngFileUpload.directive('ngfSelect', ['$parse', '$timeout', '$compile', 'Upload', function ($parse, $timeout, $compile, Upload) {
  var generatedElems = [];

  function isDelayedClickSupported(ua) {
    // fix for android native browser < 4.4 and safari windows
    var m = ua.match(/Android[^\d]*(\d+)\.(\d+)/);
    if (m && m.length > 2) {
      var v = Upload.defaults.androidFixMinorVersion || 4;
      return parseInt(m[1]) < 4 || (parseInt(m[1]) === v && parseInt(m[2]) < v);
    }

    // safari on windows
    return ua.indexOf('Chrome') === -1 && /.*Windows.*Safari.*/.test(ua);
  }

  function linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, upload) {
    /** @namespace attr.ngfSelect */
    /** @namespace attr.ngfChange */
    /** @namespace attr.ngModel */
    /** @namespace attr.ngfModelOptions */
    /** @namespace attr.ngfMultiple */
    /** @namespace attr.ngfCapture */
    /** @namespace attr.ngfValidate */
    /** @namespace attr.ngfKeep */
    var attrGetter = function (name, scope) {
      return upload.attrGetter(name, attr, scope);
    };

    function isInputTypeFile() {
      return elem[0].tagName.toLowerCase() === 'input' && attr.type && attr.type.toLowerCase() === 'file';
    }

    function fileChangeAttr() {
      return attrGetter('ngfChange') || attrGetter('ngfSelect');
    }

    function changeFn(evt) {
      if (upload.shouldUpdateOn('change', attr, scope)) {
        var fileList = evt.__files_ || (evt.target && evt.target.files), files = [];
        /* Handle duplicate call in  IE11 */
        if (!fileList) return;
        for (var i = 0; i < fileList.length; i++) {
          files.push(fileList[i]);
        }
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(),
          files.length ? files : null, evt);
      }
    }

    upload.registerModelChangeValidator(ngModel, attr, scope);

    var unwatches = [];
    if (attrGetter('ngfMultiple')) {
      unwatches.push(scope.$watch(attrGetter('ngfMultiple'), function () {
        fileElem.attr('multiple', attrGetter('ngfMultiple', scope));
      }));
    }
    if (attrGetter('ngfCapture')) {
      unwatches.push(scope.$watch(attrGetter('ngfCapture'), function () {
        fileElem.attr('capture', attrGetter('ngfCapture', scope));
      }));
    }
    if (attrGetter('ngfAccept')) {
      unwatches.push(scope.$watch(attrGetter('ngfAccept'), function () {
        fileElem.attr('accept', attrGetter('ngfAccept', scope));
      }));
    }
    unwatches.push(attr.$observe('accept', function () {
      fileElem.attr('accept', attrGetter('accept'));
    }));
    function bindAttrToFileInput(fileElem, label) {
      function updateId(val) {
        fileElem.attr('id', 'ngf-' + val);
        label.attr('id', 'ngf-label-' + val);
      }

      for (var i = 0; i < elem[0].attributes.length; i++) {
        var attribute = elem[0].attributes[i];
        if (attribute.name !== 'type' && attribute.name !== 'class' && attribute.name !== 'style') {
          if (attribute.name === 'id') {
            updateId(attribute.value);
            unwatches.push(attr.$observe('id', updateId));
          } else {
            fileElem.attr(attribute.name, (!attribute.value && (attribute.name === 'required' ||
            attribute.name === 'multiple')) ? attribute.name : attribute.value);
          }
        }
      }
    }

    function createFileInput() {
      if (isInputTypeFile()) {
        return elem;
      }

      var fileElem = angular.element('<input type="file">');

      var label = angular.element('<label>upload</label>');
      label.css('visibility', 'hidden').css('position', 'absolute').css('overflow', 'hidden')
        .css('width', '0px').css('height', '0px').css('border', 'none')
        .css('margin', '0px').css('padding', '0px').attr('tabindex', '-1');
      bindAttrToFileInput(fileElem, label);

      generatedElems.push({el: elem, ref: label});

      document.body.appendChild(label.append(fileElem)[0]);

      return fileElem;
    }

    function clickHandler(evt) {
      if (elem.attr('disabled')) return false;
      if (attrGetter('ngfSelectDisabled', scope)) return;

      var r = detectSwipe(evt);
      // prevent the click if it is a swipe
      if (r != null) return r;

      resetModel(evt);

      // fix for md when the element is removed from the DOM and added back #460
      try {
        if (!isInputTypeFile() && !document.body.contains(fileElem[0])) {
          generatedElems.push({el: elem, ref: fileElem.parent()});
          document.body.appendChild(fileElem.parent()[0]);
          fileElem.bind('change', changeFn);
        }
      } catch (e) {/*ignore*/
      }

      if (isDelayedClickSupported(navigator.userAgent)) {
        setTimeout(function () {
          fileElem[0].click();
        }, 0);
      } else {
        fileElem[0].click();
      }

      return false;
    }


    var initialTouchStartY = 0;
    var initialTouchStartX = 0;

    function detectSwipe(evt) {
      var touches = evt.changedTouches || (evt.originalEvent && evt.originalEvent.changedTouches);
      if (touches) {
        if (evt.type === 'touchstart') {
          initialTouchStartX = touches[0].clientX;
          initialTouchStartY = touches[0].clientY;
          return true; // don't block event default
        } else {
          // prevent scroll from triggering event
          if (evt.type === 'touchend') {
            var currentX = touches[0].clientX;
            var currentY = touches[0].clientY;
            if ((Math.abs(currentX - initialTouchStartX) > 20) ||
              (Math.abs(currentY - initialTouchStartY) > 20)) {
              evt.stopPropagation();
              evt.preventDefault();
              return false;
            }
          }
          return true;
        }
      }
    }

    var fileElem = elem;

    function resetModel(evt) {
      if (upload.shouldUpdateOn('click', attr, scope) && fileElem.val()) {
        fileElem.val(null);
        upload.updateModel(ngModel, attr, scope, fileChangeAttr(), null, evt, true);
      }
    }

    if (!isInputTypeFile()) {
      fileElem = createFileInput();
    }
    fileElem.bind('change', changeFn);

    if (!isInputTypeFile()) {
      elem.bind('click touchstart touchend', clickHandler);
    } else {
      elem.bind('click', resetModel);
    }

    function ie10SameFileSelectFix(evt) {
      if (fileElem && !fileElem.attr('__ngf_ie10_Fix_')) {
        if (!fileElem[0].parentNode) {
          fileElem = null;
          return;
        }
        evt.preventDefault();
        evt.stopPropagation();
        fileElem.unbind('click');
        var clone = fileElem.clone();
        fileElem.replaceWith(clone);
        fileElem = clone;
        fileElem.attr('__ngf_ie10_Fix_', 'true');
        fileElem.bind('change', changeFn);
        fileElem.bind('click', ie10SameFileSelectFix);
        fileElem[0].click();
        return false;
      } else {
        fileElem.removeAttr('__ngf_ie10_Fix_');
      }
    }

    if (navigator.appVersion.indexOf('MSIE 10') !== -1) {
      fileElem.bind('click', ie10SameFileSelectFix);
    }

    if (ngModel) ngModel.$formatters.push(function (val) {
      if (val == null || val.length === 0) {
        if (fileElem.val()) {
          fileElem.val(null);
        }
      }
      return val;
    });

    scope.$on('$destroy', function () {
      if (!isInputTypeFile()) fileElem.parent().remove();
      angular.forEach(unwatches, function (unwatch) {
        unwatch();
      });
    });

    $timeout(function () {
      for (var i = 0; i < generatedElems.length; i++) {
        var g = generatedElems[i];
        if (!document.body.contains(g.el[0])) {
          generatedElems.splice(i, 1);
          g.ref.remove();
        }
      }
    });

    if (window.FileAPI && window.FileAPI.ngfFixIE) {
      window.FileAPI.ngfFixIE(elem, fileElem, changeFn);
    }
  }

  return {
    restrict: 'AEC',
    require: '?ngModel',
    link: function (scope, elem, attr, ngModel) {
      linkFileSelect(scope, elem, attr, ngModel, $parse, $timeout, $compile, Upload);
    }
  };
}]);

(function () {

  ngFileUpload.service('UploadDataUrl', ['UploadBase', '$timeout', '$q', function (UploadBase, $timeout, $q) {
    var upload = UploadBase;
    upload.base64DataUrl = function (file) {
      if (angular.isArray(file)) {
        var d = $q.defer(), count = 0;
        angular.forEach(file, function (f) {
          upload.dataUrl(f, true)['finally'](function () {
            count++;
            if (count === file.length) {
              var urls = [];
              angular.forEach(file, function (ff) {
                urls.push(ff.$ngfDataUrl);
              });
              d.resolve(urls, file);
            }
          });
        });
        return d.promise;
      } else {
        return upload.dataUrl(file, true);
      }
    };
    upload.dataUrl = function (file, disallowObjectUrl) {
      if (!file) return upload.emptyPromise(file, file);
      if ((disallowObjectUrl && file.$ngfDataUrl != null) || (!disallowObjectUrl && file.$ngfBlobUrl != null)) {
        return upload.emptyPromise(disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl, file);
      }
      var p = disallowObjectUrl ? file.$$ngfDataUrlPromise : file.$$ngfBlobUrlPromise;
      if (p) return p;

      var deferred = $q.defer();
      $timeout(function () {
        if (window.FileReader && file &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 8') === -1 || file.size < 20000) &&
          (!window.FileAPI || navigator.userAgent.indexOf('MSIE 9') === -1 || file.size < 4000000)) {
          //prefer URL.createObjectURL for handling refrences to files of all sizes
          //since it doesn´t build a large string in memory
          var URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL && !disallowObjectUrl) {
            var url;
            try {
              url = URL.createObjectURL(file);
            } catch (e) {
              $timeout(function () {
                file.$ngfBlobUrl = '';
                deferred.reject();
              });
              return;
            }
            $timeout(function () {
              file.$ngfBlobUrl = url;
              if (url) {
                deferred.resolve(url, file);
                upload.blobUrls = upload.blobUrls || [];
                upload.blobUrlsTotalSize = upload.blobUrlsTotalSize || 0;
                upload.blobUrls.push({url: url, size: file.size});
                upload.blobUrlsTotalSize += file.size || 0;
                var maxMemory = upload.defaults.blobUrlsMaxMemory || 268435456;
                var maxLength = upload.defaults.blobUrlsMaxQueueSize || 200;
                while ((upload.blobUrlsTotalSize > maxMemory || upload.blobUrls.length > maxLength) && upload.blobUrls.length > 1) {
                  var obj = upload.blobUrls.splice(0, 1)[0];
                  URL.revokeObjectURL(obj.url);
                  upload.blobUrlsTotalSize -= obj.size;
                }
              }
            });
          } else {
            var fileReader = new FileReader();
            fileReader.onload = function (e) {
              $timeout(function () {
                file.$ngfDataUrl = e.target.result;
                deferred.resolve(e.target.result, file);
                $timeout(function () {
                  delete file.$ngfDataUrl;
                }, 1000);
              });
            };
            fileReader.onerror = function () {
              $timeout(function () {
                file.$ngfDataUrl = '';
                deferred.reject();
              });
            };
            fileReader.readAsDataURL(file);
          }
        } else {
          $timeout(function () {
            file[disallowObjectUrl ? '$ngfDataUrl' : '$ngfBlobUrl'] = '';
            deferred.reject();
          });
        }
      });

      if (disallowObjectUrl) {
        p = file.$$ngfDataUrlPromise = deferred.promise;
      } else {
        p = file.$$ngfBlobUrlPromise = deferred.promise;
      }
      p['finally'](function () {
        delete file[disallowObjectUrl ? '$$ngfDataUrlPromise' : '$$ngfBlobUrlPromise'];
      });
      return p;
    };
    return upload;
  }]);

  function getTagType(el) {
    if (el.tagName.toLowerCase() === 'img') return 'image';
    if (el.tagName.toLowerCase() === 'audio') return 'audio';
    if (el.tagName.toLowerCase() === 'video') return 'video';
    return /./;
  }

  function linkFileDirective(Upload, $timeout, scope, elem, attr, directiveName, resizeParams, isBackground) {
    function constructDataUrl(file) {
      var disallowObjectUrl = Upload.attrGetter('ngfNoObjectUrl', attr, scope);
      Upload.dataUrl(file, disallowObjectUrl)['finally'](function () {
        $timeout(function () {
          var src = (disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl;
          if (isBackground) {
            elem.css('background-image', 'url(\'' + (src || '') + '\')');
          } else {
            elem.attr('src', src);
          }
          if (src) {
            elem.removeClass('ng-hide');
          } else {
            elem.addClass('ng-hide');
          }
        });
      });
    }

    $timeout(function () {
      var unwatch = scope.$watch(attr[directiveName], function (file) {
        var size = resizeParams;
        if (directiveName === 'ngfThumbnail') {
          if (!size) {
            size = {
              width: elem[0].naturalWidth || elem[0].clientWidth,
              height: elem[0].naturalHeight || elem[0].clientHeight
            };
          }
          if (size.width === 0 && window.getComputedStyle) {
            var style = getComputedStyle(elem[0]);
            if (style.width && style.width.indexOf('px') > -1 && style.height && style.height.indexOf('px') > -1) {
              size = {
                width: parseInt(style.width.slice(0, -2)),
                height: parseInt(style.height.slice(0, -2))
              };
            }
          }
        }

        if (angular.isString(file)) {
          elem.removeClass('ng-hide');
          if (isBackground) {
            return elem.css('background-image', 'url(\'' + file + '\')');
          } else {
            return elem.attr('src', file);
          }
        }
        if (file && file.type && file.type.search(getTagType(elem[0])) === 0 &&
          (!isBackground || file.type.indexOf('image') === 0)) {
          if (size && Upload.isResizeSupported()) {
            size.resizeIf = function (width, height) {
              return Upload.attrGetter('ngfResizeIf', attr, scope,
                {$width: width, $height: height, $file: file});
            };
            Upload.resize(file, size).then(
              function (f) {
                constructDataUrl(f);
              }, function (e) {
                throw e;
              }
            );
          } else {
            constructDataUrl(file);
          }
        } else {
          elem.addClass('ng-hide');
        }
      });

      scope.$on('$destroy', function () {
        unwatch();
      });
    });
  }


  /** @namespace attr.ngfSrc */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfSrc', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfSrc',
          Upload.attrGetter('ngfResize', attr, scope), false);
      }
    };
  }]);

  /** @namespace attr.ngfBackground */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfBackground', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfBackground',
          Upload.attrGetter('ngfResize', attr, scope), true);
      }
    };
  }]);

  /** @namespace attr.ngfThumbnail */
  /** @namespace attr.ngfAsBackground */
  /** @namespace attr.ngfSize */
  /** @namespace attr.ngfNoObjectUrl */
  ngFileUpload.directive('ngfThumbnail', ['Upload', '$timeout', function (Upload, $timeout) {
    return {
      restrict: 'AE',
      link: function (scope, elem, attr) {
        var size = Upload.attrGetter('ngfSize', attr, scope);
        linkFileDirective(Upload, $timeout, scope, elem, attr, 'ngfThumbnail', size,
          Upload.attrGetter('ngfAsBackground', attr, scope));
      }
    };
  }]);

  ngFileUpload.config(['$compileProvider', function ($compileProvider) {
    if ($compileProvider.imgSrcSanitizationWhitelist) $compileProvider.imgSrcSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|webcal|local|file|data|blob):/);
    if ($compileProvider.aHrefSanitizationWhitelist) $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|tel|webcal|local|file|data|blob):/);
  }]);

  ngFileUpload.filter('ngfDataUrl', ['UploadDataUrl', '$sce', function (UploadDataUrl, $sce) {
    return function (file, disallowObjectUrl, trustedUrl) {
      if (angular.isString(file)) {
        return $sce.trustAsResourceUrl(file);
      }
      var src = file && ((disallowObjectUrl ? file.$ngfDataUrl : file.$ngfBlobUrl) || file.$ngfDataUrl);
      if (file && !src) {
        if (!file.$ngfDataUrlFilterInProgress && angular.isObject(file)) {
          file.$ngfDataUrlFilterInProgress = true;
          UploadDataUrl.dataUrl(file, disallowObjectUrl);
        }
        return '';
      }
      if (file) delete file.$ngfDataUrlFilterInProgress;
      return (file && src ? (trustedUrl ? $sce.trustAsResourceUrl(src) : src) : file) || '';
    };
  }]);

})();

ngFileUpload.service('UploadValidate', ['UploadDataUrl', '$q', '$timeout', function (UploadDataUrl, $q, $timeout) {
  var upload = UploadDataUrl;

  function globStringToRegex(str) {
    var regexp = '', excludes = [];
    if (str.length > 2 && str[0] === '/' && str[str.length - 1] === '/') {
      regexp = str.substring(1, str.length - 1);
    } else {
      var split = str.split(',');
      if (split.length > 1) {
        for (var i = 0; i < split.length; i++) {
          var r = globStringToRegex(split[i]);
          if (r.regexp) {
            regexp += '(' + r.regexp + ')';
            if (i < split.length - 1) {
              regexp += '|';
            }
          } else {
            excludes = excludes.concat(r.excludes);
          }
        }
      } else {
        if (str.indexOf('!') === 0) {
          excludes.push('^((?!' + globStringToRegex(str.substring(1)).regexp + ').)*$');
        } else {
          if (str.indexOf('.') === 0) {
            str = '*' + str;
          }
          regexp = '^' + str.replace(new RegExp('[.\\\\+*?\\[\\^\\]$(){}=!<>|:\\-]', 'g'), '\\$&') + '$';
          regexp = regexp.replace(/\\\*/g, '.*').replace(/\\\?/g, '.');
        }
      }
    }
    return {regexp: regexp, excludes: excludes};
  }

  upload.validatePattern = function (file, val) {
    if (!val) {
      return true;
    }
    var pattern = globStringToRegex(val), valid = true;
    if (pattern.regexp && pattern.regexp.length) {
      var regexp = new RegExp(pattern.regexp, 'i');
      valid = (file.type != null && regexp.test(file.type)) ||
        (file.name != null && regexp.test(file.name));
    }
    var len = pattern.excludes.length;
    while (len--) {
      var exclude = new RegExp(pattern.excludes[len], 'i');
      valid = valid && (file.type == null || exclude.test(file.type)) &&
        (file.name == null || exclude.test(file.name));
    }
    return valid;
  };

  upload.ratioToFloat = function (val) {
    var r = val.toString(), xIndex = r.search(/[x:]/i);
    if (xIndex > -1) {
      r = parseFloat(r.substring(0, xIndex)) / parseFloat(r.substring(xIndex + 1));
    } else {
      r = parseFloat(r);
    }
    return r;
  };

  upload.registerModelChangeValidator = function (ngModel, attr, scope) {
    if (ngModel) {
      ngModel.$formatters.push(function (files) {
        if (ngModel.$dirty) {
          var filesArray = files;
          if (files && !angular.isArray(files)) {
            filesArray = [files];
          }
          upload.validate(filesArray, 0, ngModel, attr, scope).then(function () {
            upload.applyModelValidation(ngModel, filesArray);
          });
        }
        return files;
      });
    }
  };

  function markModelAsDirty(ngModel, files) {
    if (files != null && !ngModel.$dirty) {
      if (ngModel.$setDirty) {
        ngModel.$setDirty();
      } else {
        ngModel.$dirty = true;
      }
    }
  }

  upload.applyModelValidation = function (ngModel, files) {
    markModelAsDirty(ngModel, files);
    angular.forEach(ngModel.$ngfValidations, function (validation) {
      ngModel.$setValidity(validation.name, validation.valid);
    });
  };

  upload.getValidationAttr = function (attr, scope, name, validationName, file) {
    var dName = 'ngf' + name[0].toUpperCase() + name.substr(1);
    var val = upload.attrGetter(dName, attr, scope, {$file: file});
    if (val == null) {
      val = upload.attrGetter('ngfValidate', attr, scope, {$file: file});
      if (val) {
        var split = (validationName || name).split('.');
        val = val[split[0]];
        if (split.length > 1) {
          val = val && val[split[1]];
        }
      }
    }
    return val;
  };

  upload.validate = function (files, prevLength, ngModel, attr, scope) {
    ngModel = ngModel || {};
    ngModel.$ngfValidations = ngModel.$ngfValidations || [];

    angular.forEach(ngModel.$ngfValidations, function (v) {
      v.valid = true;
    });

    var attrGetter = function (name, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    var ignoredErrors = (upload.attrGetter('ngfIgnoreInvalid', attr, scope) || '').split(' ');
    var runAllValidation = upload.attrGetter('ngfRunAllValidations', attr, scope);

    if (files == null || files.length === 0) {
      return upload.emptyPromise({'validFiles': files, 'invalidFiles': []});
    }

    files = files.length === undefined ? [files] : files.slice(0);
    var invalidFiles = [];

    function validateSync(name, validationName, fn) {
      if (files) {
        var i = files.length, valid = null;
        while (i--) {
          var file = files[i];
          if (file) {
            var val = upload.getValidationAttr(attr, scope, name, validationName, file);
            if (val != null) {
              if (!fn(file, val, i)) {
                if (ignoredErrors.indexOf(name) === -1) {
                  file.$error = name;
                  (file.$errorMessages = (file.$errorMessages || {}))[name] = true;
                  file.$errorParam = val;
                  if (invalidFiles.indexOf(file) === -1) {
                    invalidFiles.push(file);
                  }
                  if (!runAllValidation) {
                    files.splice(i, 1);
                  }
                  valid = false;
                } else {
                  files.splice(i, 1);
                }
              }
            }
          }
        }
        if (valid !== null) {
          ngModel.$ngfValidations.push({name: name, valid: valid});
        }
      }
    }

    validateSync('pattern', null, upload.validatePattern);
    validateSync('minSize', 'size.min', function (file, val) {
      return file.size + 0.1 >= upload.translateScalars(val);
    });
    validateSync('maxSize', 'size.max', function (file, val) {
      return file.size - 0.1 <= upload.translateScalars(val);
    });
    var totalSize = 0;
    validateSync('maxTotalSize', null, function (file, val) {
      totalSize += file.size;
      if (totalSize > upload.translateScalars(val)) {
        files.splice(0, files.length);
        return false;
      }
      return true;
    });

    validateSync('validateFn', null, function (file, r) {
      return r === true || r === null || r === '';
    });

    if (!files.length) {
      return upload.emptyPromise({'validFiles': [], 'invalidFiles': invalidFiles});
    }

    function validateAsync(name, validationName, type, asyncFn, fn) {
      function resolveResult(defer, file, val) {
        function resolveInternal(fn) {
          if (fn()) {
            if (ignoredErrors.indexOf(name) === -1) {
              file.$error = name;
              (file.$errorMessages = (file.$errorMessages || {}))[name] = true;
              file.$errorParam = val;
              if (invalidFiles.indexOf(file) === -1) {
                invalidFiles.push(file);
              }
              if (!runAllValidation) {
                var i = files.indexOf(file);
                if (i > -1) files.splice(i, 1);
              }
              defer.resolve(false);
            } else {
              var j = files.indexOf(file);
              if (j > -1) files.splice(j, 1);
              defer.resolve(true);
            }
          } else {
            defer.resolve(true);
          }
        }

        if (val != null) {
          asyncFn(file, val).then(function (d) {
            resolveInternal(function () {
              return !fn(d, val);
            });
          }, function () {
            resolveInternal(function () {
              return attrGetter('ngfValidateForce', {$file: file});
            });
          });
        } else {
          defer.resolve(true);
        }
      }

      var promises = [upload.emptyPromise(true)];
      if (files) {
        files = files.length === undefined ? [files] : files;
        angular.forEach(files, function (file) {
          var defer = $q.defer();
          promises.push(defer.promise);
          if (type && (file.type == null || file.type.search(type) !== 0)) {
            defer.resolve(true);
            return;
          }
          if (name === 'dimensions' && upload.attrGetter('ngfDimensions', attr) != null) {
            upload.imageDimensions(file).then(function (d) {
              resolveResult(defer, file,
                attrGetter('ngfDimensions', {$file: file, $width: d.width, $height: d.height}));
            }, function () {
              defer.resolve(false);
            });
          } else if (name === 'duration' && upload.attrGetter('ngfDuration', attr) != null) {
            upload.mediaDuration(file).then(function (d) {
              resolveResult(defer, file,
                attrGetter('ngfDuration', {$file: file, $duration: d}));
            }, function () {
              defer.resolve(false);
            });
          } else {
            resolveResult(defer, file,
              upload.getValidationAttr(attr, scope, name, validationName, file));
          }
        });
      }
      var deffer = $q.defer();
      $q.all(promises).then(function (values) {
        var isValid = true;
        for (var i = 0; i < values.length; i++) {
          if (!values[i]) {
            isValid = false;
            break;
          }
        }
        ngModel.$ngfValidations.push({name: name, valid: isValid});
        deffer.resolve(isValid);
      });
      return deffer.promise;
    }

    var deffer = $q.defer();
    var promises = [];

    promises.push(validateAsync('maxHeight', 'height.max', /image/,
      this.imageDimensions, function (d, val) {
        return d.height <= val;
      }));
    promises.push(validateAsync('minHeight', 'height.min', /image/,
      this.imageDimensions, function (d, val) {
        return d.height >= val;
      }));
    promises.push(validateAsync('maxWidth', 'width.max', /image/,
      this.imageDimensions, function (d, val) {
        return d.width <= val;
      }));
    promises.push(validateAsync('minWidth', 'width.min', /image/,
      this.imageDimensions, function (d, val) {
        return d.width >= val;
      }));
    promises.push(validateAsync('dimensions', null, /image/,
      function (file, val) {
        return upload.emptyPromise(val);
      }, function (r) {
        return r;
      }));
    promises.push(validateAsync('ratio', null, /image/,
      this.imageDimensions, function (d, val) {
        var split = val.toString().split(','), valid = false;
        for (var i = 0; i < split.length; i++) {
          if (Math.abs((d.width / d.height) - upload.ratioToFloat(split[i])) < 0.01) {
            valid = true;
          }
        }
        return valid;
      }));
    promises.push(validateAsync('maxRatio', 'ratio.max', /image/,
      this.imageDimensions, function (d, val) {
        return (d.width / d.height) - upload.ratioToFloat(val) < 0.0001;
      }));
    promises.push(validateAsync('minRatio', 'ratio.min', /image/,
      this.imageDimensions, function (d, val) {
        return (d.width / d.height) - upload.ratioToFloat(val) > -0.0001;
      }));
    promises.push(validateAsync('maxDuration', 'duration.max', /audio|video/,
      this.mediaDuration, function (d, val) {
        return d <= upload.translateScalars(val);
      }));
    promises.push(validateAsync('minDuration', 'duration.min', /audio|video/,
      this.mediaDuration, function (d, val) {
        return d >= upload.translateScalars(val);
      }));
    promises.push(validateAsync('duration', null, /audio|video/,
      function (file, val) {
        return upload.emptyPromise(val);
      }, function (r) {
        return r;
      }));

    promises.push(validateAsync('validateAsyncFn', null, null,
      function (file, val) {
        return val;
      }, function (r) {
        return r === true || r === null || r === '';
      }));

    $q.all(promises).then(function () {

      if (runAllValidation) {
        for (var i = 0; i < files.length; i++) {
          var file = files[i];
          if (file.$error) {
            files.splice(i--, 1);
          }
        }
      }

      runAllValidation = false;
      validateSync('maxFiles', null, function (file, val, i) {
        return prevLength + i < val;
      });

      deffer.resolve({'validFiles': files, 'invalidFiles': invalidFiles});
    });
    return deffer.promise;
  };

  upload.imageDimensions = function (file) {
    if (file.$ngfWidth && file.$ngfHeight) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve({width: file.$ngfWidth, height: file.$ngfHeight});
      });
      return d.promise;
    }
    if (file.$ngfDimensionPromise) return file.$ngfDimensionPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('image') !== 0) {
        deferred.reject('not image');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var img = angular.element('<img>').attr('src', dataUrl)
          .css('visibility', 'hidden').css('position', 'fixed')
          .css('max-width', 'none !important').css('max-height', 'none !important');

        function success() {
          var width = img[0].naturalWidth || img[0].clientWidth;
          var height = img[0].naturalHeight || img[0].clientHeight;
          img.remove();
          file.$ngfWidth = width;
          file.$ngfHeight = height;
          deferred.resolve({width: width, height: height});
        }

        function error() {
          img.remove();
          deferred.reject('load error');
        }

        img.on('load', success);
        img.on('error', error);

        var secondsCounter = 0;
        function checkLoadErrorInCaseOfNoCallback() {
          $timeout(function () {
            if (img[0].parentNode) {
              if (img[0].clientWidth) {
                success();
              } else if (secondsCounter++ > 10) {
                error();
              } else {
                checkLoadErrorInCaseOfNoCallback();
              }
            }
          }, 1000);
        }

        checkLoadErrorInCaseOfNoCallback();

        angular.element(document.getElementsByTagName('body')[0]).append(img);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDimensionPromise = deferred.promise;
    file.$ngfDimensionPromise['finally'](function () {
      delete file.$ngfDimensionPromise;
    });
    return file.$ngfDimensionPromise;
  };

  upload.mediaDuration = function (file) {
    if (file.$ngfDuration) {
      var d = $q.defer();
      $timeout(function () {
        d.resolve(file.$ngfDuration);
      });
      return d.promise;
    }
    if (file.$ngfDurationPromise) return file.$ngfDurationPromise;

    var deferred = $q.defer();
    $timeout(function () {
      if (file.type.indexOf('audio') !== 0 && file.type.indexOf('video') !== 0) {
        deferred.reject('not media');
        return;
      }
      upload.dataUrl(file).then(function (dataUrl) {
        var el = angular.element(file.type.indexOf('audio') === 0 ? '<audio>' : '<video>')
          .attr('src', dataUrl).css('visibility', 'none').css('position', 'fixed');

        function success() {
          var duration = el[0].duration;
          file.$ngfDuration = duration;
          el.remove();
          deferred.resolve(duration);
        }

        function error() {
          el.remove();
          deferred.reject('load error');
        }

        el.on('loadedmetadata', success);
        el.on('error', error);
        var count = 0;

        function checkLoadError() {
          $timeout(function () {
            if (el[0].parentNode) {
              if (el[0].duration) {
                success();
              } else if (count > 10) {
                error();
              } else {
                checkLoadError();
              }
            }
          }, 1000);
        }

        checkLoadError();

        angular.element(document.body).append(el);
      }, function () {
        deferred.reject('load error');
      });
    });

    file.$ngfDurationPromise = deferred.promise;
    file.$ngfDurationPromise['finally'](function () {
      delete file.$ngfDurationPromise;
    });
    return file.$ngfDurationPromise;
  };
  return upload;
}
]);

ngFileUpload.service('UploadResize', ['UploadValidate', '$q', function (UploadValidate, $q) {
  var upload = UploadValidate;

  /**
   * Conserve aspect ratio of the original region. Useful when shrinking/enlarging
   * images to fit into a certain area.
   * Source:  http://stackoverflow.com/a/14731922
   *
   * @param {Number} srcWidth Source area width
   * @param {Number} srcHeight Source area height
   * @param {Number} maxWidth Nestable area maximum available width
   * @param {Number} maxHeight Nestable area maximum available height
   * @return {Object} { width, height }
   */
  var calculateAspectRatioFit = function (srcWidth, srcHeight, maxWidth, maxHeight, centerCrop) {
    var ratio = centerCrop ? Math.max(maxWidth / srcWidth, maxHeight / srcHeight) :
      Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {
      width: srcWidth * ratio, height: srcHeight * ratio,
      marginX: srcWidth * ratio - maxWidth, marginY: srcHeight * ratio - maxHeight
    };
  };

  // Extracted from https://github.com/romelgomez/angular-firebase-image-upload/blob/master/app/scripts/fileUpload.js#L89
  var resize = function (imagen, width, height, quality, type, ratio, centerCrop, resizeIf) {
    var deferred = $q.defer();
    var canvasElement = document.createElement('canvas');
    var imageElement = document.createElement('img');
    imageElement.setAttribute('style', 'visibility:hidden;position:fixed;z-index:-100000');
    document.body.appendChild(imageElement);

    imageElement.onload = function () {
      var imgWidth = imageElement.width, imgHeight = imageElement.height;
      imageElement.parentNode.removeChild(imageElement);
      if (resizeIf != null && resizeIf(imgWidth, imgHeight) === false) {
        deferred.reject('resizeIf');
        return;
      }
      try {
        if (ratio) {
          var ratioFloat = upload.ratioToFloat(ratio);
          var imgRatio = imgWidth / imgHeight;
          if (imgRatio < ratioFloat) {
            width = imgWidth;
            height = width / ratioFloat;
          } else {
            height = imgHeight;
            width = height * ratioFloat;
          }
        }
        if (!width) {
          width = imgWidth;
        }
        if (!height) {
          height = imgHeight;
        }
        var dimensions = calculateAspectRatioFit(imgWidth, imgHeight, width, height, centerCrop);
        canvasElement.width = Math.min(dimensions.width, width);
        canvasElement.height = Math.min(dimensions.height, height);
        var context = canvasElement.getContext('2d');
        context.drawImage(imageElement,
          Math.min(0, -dimensions.marginX / 2), Math.min(0, -dimensions.marginY / 2),
          dimensions.width, dimensions.height);
        deferred.resolve(canvasElement.toDataURL(type || 'image/WebP', quality || 0.934));
      } catch (e) {
        deferred.reject(e);
      }
    };
    imageElement.onerror = function () {
      imageElement.parentNode.removeChild(imageElement);
      deferred.reject();
    };
    imageElement.src = imagen;
    return deferred.promise;
  };

  upload.dataUrltoBlob = function (dataurl, name, origSize) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    var blob = new window.Blob([u8arr], {type: mime});
    blob.name = name;
    blob.$ngfOrigSize = origSize;
    return blob;
  };

  upload.isResizeSupported = function () {
    var elem = document.createElement('canvas');
    return window.atob && elem.getContext && elem.getContext('2d') && window.Blob;
  };

  if (upload.isResizeSupported()) {
    // add name getter to the blob constructor prototype
    Object.defineProperty(window.Blob.prototype, 'name', {
      get: function () {
        return this.$ngfName;
      },
      set: function (v) {
        this.$ngfName = v;
      },
      configurable: true
    });
  }

  upload.resize = function (file, options) {
    if (file.type.indexOf('image') !== 0) return upload.emptyPromise(file);

    var deferred = $q.defer();
    upload.dataUrl(file, true).then(function (url) {
      resize(url, options.width, options.height, options.quality, options.type || file.type,
        options.ratio, options.centerCrop, options.resizeIf)
        .then(function (dataUrl) {
          if (file.type === 'image/jpeg' && options.restoreExif !== false) {
            try {
              dataUrl = upload.restoreExif(url, dataUrl);
            } catch (e) {
              setTimeout(function () {throw e;}, 1);
            }
          }
          try {
            var blob = upload.dataUrltoBlob(dataUrl, file.name, file.size);
            deferred.resolve(blob);
          } catch (e) {
            deferred.reject(e);
          }
        }, function (r) {
          if (r === 'resizeIf') {
            deferred.resolve(file);
          }
          deferred.reject(r);
        });
    }, function (e) {
      deferred.reject(e);
    });
    return deferred.promise;
  };

  return upload;
}]);

(function () {
  ngFileUpload.directive('ngfDrop', ['$parse', '$timeout', '$window', 'Upload', '$http', '$q',
    function ($parse, $timeout, $window, Upload, $http, $q) {
      return {
        restrict: 'AEC',
        require: '?ngModel',
        link: function (scope, elem, attr, ngModel) {
          linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $window, Upload, $http, $q);
        }
      };
    }]);

  ngFileUpload.directive('ngfNoFileDrop', function () {
    return function (scope, elem) {
      if (dropAvailable()) elem.css('display', 'none');
    };
  });

  ngFileUpload.directive('ngfDropAvailable', ['$parse', '$timeout', 'Upload', function ($parse, $timeout, Upload) {
    return function (scope, elem, attr) {
      if (dropAvailable()) {
        var model = $parse(Upload.attrGetter('ngfDropAvailable', attr));
        $timeout(function () {
          model(scope);
          if (model.assign) {
            model.assign(scope, true);
          }
        });
      }
    };
  }]);

  function linkDrop(scope, elem, attr, ngModel, $parse, $timeout, $window, upload, $http, $q) {
    var available = dropAvailable();

    var attrGetter = function (name, scope, params) {
      return upload.attrGetter(name, attr, scope, params);
    };

    if (attrGetter('dropAvailable')) {
      $timeout(function () {
        if (scope[attrGetter('dropAvailable')]) {
          scope[attrGetter('dropAvailable')].value = available;
        } else {
          scope[attrGetter('dropAvailable')] = available;
        }
      });
    }
    if (!available) {
      if (attrGetter('ngfHideOnDropNotAvailable', scope) === true) {
        elem.css('display', 'none');
      }
      return;
    }

    function isDisabled() {
      return elem.attr('disabled') || attrGetter('ngfDropDisabled', scope);
    }

    if (attrGetter('ngfSelect') == null) {
      upload.registerModelChangeValidator(ngModel, attr, scope);
    }

    var leaveTimeout = null;
    var stopPropagation = $parse(attrGetter('ngfStopPropagation'));
    var dragOverDelay = 1;
    var actualDragOverClass;

    elem[0].addEventListener('dragover', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      // handling dragover events from the Chrome download bar
      if (navigator.userAgent.indexOf('Chrome') > -1) {
        var b = evt.dataTransfer.effectAllowed;
        evt.dataTransfer.dropEffect = ('move' === b || 'linkMove' === b) ? 'move' : 'copy';
      }
      $timeout.cancel(leaveTimeout);
      if (!actualDragOverClass) {
        actualDragOverClass = 'C';
        calculateDragOverClass(scope, attr, evt, function (clazz) {
          actualDragOverClass = clazz;
          elem.addClass(actualDragOverClass);
          attrGetter('ngfDrag', scope, {$isDragging: true, $class: actualDragOverClass, $event: evt});
        });
      }
    }, false);
    elem[0].addEventListener('dragenter', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
    }, false);
    elem[0].addEventListener('dragleave', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      leaveTimeout = $timeout(function () {
        if (actualDragOverClass) elem.removeClass(actualDragOverClass);
        actualDragOverClass = null;
        attrGetter('ngfDrag', scope, {$isDragging: false, $event: evt});
      }, dragOverDelay || 100);
    }, false);
    elem[0].addEventListener('drop', function (evt) {
      if (isDisabled() || !upload.shouldUpdateOn('drop', attr, scope)) return;
      evt.preventDefault();
      if (stopPropagation(scope)) evt.stopPropagation();
      if (actualDragOverClass) elem.removeClass(actualDragOverClass);
      actualDragOverClass = null;
      extractFilesAndUpdateModel(evt.dataTransfer, evt, 'dropUrl');
    }, false);
    elem[0].addEventListener('paste', function (evt) {
      if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1 &&
        attrGetter('ngfEnableFirefoxPaste', scope)) {
        evt.preventDefault();
      }
      if (isDisabled() || !upload.shouldUpdateOn('paste', attr, scope)) return;
      extractFilesAndUpdateModel(evt.clipboardData || evt.originalEvent.clipboardData, evt, 'pasteUrl');
    }, false);

    if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1 &&
      attrGetter('ngfEnableFirefoxPaste', scope)) {
      elem.attr('contenteditable', true);
      elem.on('keypress', function (e) {
        if (!e.metaKey && !e.ctrlKey) {
          e.preventDefault();
        }
      });
    }

    function extractFilesAndUpdateModel(source, evt, updateOnType) {
      if (!source) return;
      // html needs to be calculated on the same process otherwise the data will be wiped
      // after promise resolve or setTimeout.
      var html;
      try {
        html = source && source.getData && source.getData('text/html');
      } catch (e) {/* Fix IE11 that throw error calling getData */
      }
      extractFiles(source.items, source.files, attrGetter('ngfAllowDir', scope) !== false,
        attrGetter('multiple') || attrGetter('ngfMultiple', scope)).then(function (files) {
        if (files.length) {
          updateModel(files, evt);
        } else {
          extractFilesFromHtml(updateOnType, html).then(function (files) {
            updateModel(files, evt);
          });
        }
      });
    }

    function updateModel(files, evt) {
      upload.updateModel(ngModel, attr, scope, attrGetter('ngfChange') || attrGetter('ngfDrop'), files, evt);
    }

    function extractFilesFromHtml(updateOn, html) {
      if (!upload.shouldUpdateOn(updateOn, attr, scope) || typeof html !== 'string') return upload.rejectPromise([]);
      var urls = [];
      html.replace(/<(img src|img [^>]* src) *=\"([^\"]*)\"/gi, function (m, n, src) {
        urls.push(src);
      });
      var promises = [], files = [];
      if (urls.length) {
        angular.forEach(urls, function (url) {
          promises.push(upload.urlToBlob(url).then(function (blob) {
            files.push(blob);
          }));
        });
        var defer = $q.defer();
        $q.all(promises).then(function () {
          defer.resolve(files);
        }, function (e) {
          defer.reject(e);
        });
        return defer.promise;
      }
      return upload.emptyPromise();
    }

    function calculateDragOverClass(scope, attr, evt, callback) {
      var obj = attrGetter('ngfDragOverClass', scope, {$event: evt}), dClass = 'dragover';
      if (angular.isString(obj)) {
        dClass = obj;
      } else if (obj) {
        if (obj.delay) dragOverDelay = obj.delay;
        if (obj.accept || obj.reject) {
          var items = evt.dataTransfer.items;
          if (items == null || !items.length) {
            dClass = obj.accept;
          } else {
            var pattern = obj.pattern || attrGetter('ngfPattern', scope, {$event: evt});
            var len = items.length;
            while (len--) {
              if (!upload.validatePattern(items[len], pattern)) {
                dClass = obj.reject;
                break;
              } else {
                dClass = obj.accept;
              }
            }
          }
        }
      }
      callback(dClass);
    }

    function extractFiles(items, fileList, allowDir, multiple) {
      var maxFiles = upload.getValidationAttr(attr, scope, 'maxFiles');
      if (maxFiles == null) {
        maxFiles = Number.MAX_VALUE;
      }
      var maxTotalSize = upload.getValidationAttr(attr, scope, 'maxTotalSize');
      if (maxTotalSize == null) {
        maxTotalSize = Number.MAX_VALUE;
      }
      var includeDir = attrGetter('ngfIncludeDir', scope);
      var files = [], totalSize = 0;

      function traverseFileTree(entry, path) {
        var defer = $q.defer();
        if (entry != null) {
          if (entry.isDirectory) {
            var promises = [upload.emptyPromise()];
            if (includeDir) {
              var file = {type: 'directory'};
              file.name = file.path = (path || '') + entry.name;
              files.push(file);
            }
            var dirReader = entry.createReader();
            var entries = [];
            var readEntries = function () {
              dirReader.readEntries(function (results) {
                try {
                  if (!results.length) {
                    angular.forEach(entries.slice(0), function (e) {
                      if (files.length <= maxFiles && totalSize <= maxTotalSize) {
                        promises.push(traverseFileTree(e, (path ? path : '') + entry.name + '/'));
                      }
                    });
                    $q.all(promises).then(function () {
                      defer.resolve();
                    }, function (e) {
                      defer.reject(e);
                    });
                  } else {
                    entries = entries.concat(Array.prototype.slice.call(results || [], 0));
                    readEntries();
                  }
                } catch (e) {
                  defer.reject(e);
                }
              }, function (e) {
                defer.reject(e);
              });
            };
            readEntries();
          } else {
            entry.file(function (file) {
              try {
                file.path = (path ? path : '') + file.name;
                if (includeDir) {
                  file = upload.rename(file, file.path);
                }
                files.push(file);
                totalSize += file.size;
                defer.resolve();
              } catch (e) {
                defer.reject(e);
              }
            }, function (e) {
              defer.reject(e);
            });
          }
        }
        return defer.promise;
      }

      var promises = [upload.emptyPromise()];

      if (items && items.length > 0 && $window.location.protocol !== 'file:') {
        for (var i = 0; i < items.length; i++) {
          if (items[i].webkitGetAsEntry && items[i].webkitGetAsEntry() && items[i].webkitGetAsEntry().isDirectory) {
            var entry = items[i].webkitGetAsEntry();
            if (entry.isDirectory && !allowDir) {
              continue;
            }
            if (entry != null) {
              promises.push(traverseFileTree(entry));
            }
          } else {
            var f = items[i].getAsFile();
            if (f != null) {
              files.push(f);
              totalSize += f.size;
            }
          }
          if (files.length > maxFiles || totalSize > maxTotalSize ||
            (!multiple && files.length > 0)) break;
        }
      } else {
        if (fileList != null) {
          for (var j = 0; j < fileList.length; j++) {
            var file = fileList.item(j);
            if (file.type || file.size > 0) {
              files.push(file);
              totalSize += file.size;
            }
            if (files.length > maxFiles || totalSize > maxTotalSize ||
              (!multiple && files.length > 0)) break;
          }
        }
      }

      var defer = $q.defer();
      $q.all(promises).then(function () {
        if (!multiple && !includeDir && files.length) {
          var i = 0;
          while (files[i] && files[i].type === 'directory') i++;
          defer.resolve([files[i]]);
        } else {
          defer.resolve(files);
        }
      }, function (e) {
        defer.reject(e);
      });

      return defer.promise;
    }
  }

  function dropAvailable() {
    var div = document.createElement('div');
    return ('draggable' in div) && ('ondrop' in div) && !/Edge\/12./i.test(navigator.userAgent);
  }

})();

// customized version of https://github.com/exif-js/exif-js
ngFileUpload.service('UploadExif', ['UploadResize', '$q', function (UploadResize, $q) {
  var upload = UploadResize;

  upload.isExifSupported = function () {
    return window.FileReader && new FileReader().readAsArrayBuffer && upload.isResizeSupported();
  };

  function applyTransform(ctx, orientation, width, height) {
    switch (orientation) {
      case 2:
        return ctx.transform(-1, 0, 0, 1, width, 0);
      case 3:
        return ctx.transform(-1, 0, 0, -1, width, height);
      case 4:
        return ctx.transform(1, 0, 0, -1, 0, height);
      case 5:
        return ctx.transform(0, 1, 1, 0, 0, 0);
      case 6:
        return ctx.transform(0, 1, -1, 0, height, 0);
      case 7:
        return ctx.transform(0, -1, -1, 0, height, width);
      case 8:
        return ctx.transform(0, -1, 1, 0, 0, width);
    }
  }

  upload.readOrientation = function (file) {
    var defer = $q.defer();
    var reader = new FileReader();
    var slicedFile = file.slice ? file.slice(0, 64 * 1024) : file;
    reader.readAsArrayBuffer(slicedFile);
    reader.onerror = function (e) {
      return defer.reject(e);
    };
    reader.onload = function (e) {
      var result = {orientation: 1};
      var view = new DataView(this.result);
      if (view.getUint16(0, false) !== 0xFFD8) return defer.resolve(result);

      var length = view.byteLength,
        offset = 2;
      while (offset < length) {
        var marker = view.getUint16(offset, false);
        offset += 2;
        if (marker === 0xFFE1) {
          if (view.getUint32(offset += 2, false) !== 0x45786966) return defer.resolve(result);

          var little = view.getUint16(offset += 6, false) === 0x4949;
          offset += view.getUint32(offset + 4, little);
          var tags = view.getUint16(offset, little);
          offset += 2;
          for (var i = 0; i < tags; i++)
            if (view.getUint16(offset + (i * 12), little) === 0x0112) {
              var orientation = view.getUint16(offset + (i * 12) + 8, little);
              if (orientation >= 2 && orientation <= 8) {
                view.setUint16(offset + (i * 12) + 8, 1, little);
                result.fixedArrayBuffer = e.target.result;
              }
              result.orientation = orientation;
              return defer.resolve(result);
            }
        } else if ((marker & 0xFF00) !== 0xFF00) break;
        else offset += view.getUint16(offset, false);
      }
      return defer.resolve(result);
    };
    return defer.promise;
  };

  function arrayBufferToBase64(buffer) {
    var binary = '';
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;
    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  upload.applyExifRotation = function (file) {
    if (file.type.indexOf('image/jpeg') !== 0) {
      return upload.emptyPromise(file);
    }

    var deferred = $q.defer();
    upload.readOrientation(file).then(function (result) {
      if (result.orientation < 2 || result.orientation > 8) {
        return deferred.resolve(file);
      }
      upload.dataUrl(file, true).then(function (url) {
        var canvas = document.createElement('canvas');
        var img = document.createElement('img');

        img.onload = function () {
          try {
            canvas.width = result.orientation > 4 ? img.height : img.width;
            canvas.height = result.orientation > 4 ? img.width : img.height;
            var ctx = canvas.getContext('2d');
            applyTransform(ctx, result.orientation, img.width, img.height);
            ctx.drawImage(img, 0, 0);
            var dataUrl = canvas.toDataURL(file.type || 'image/WebP', 0.934);
            dataUrl = upload.restoreExif(arrayBufferToBase64(result.fixedArrayBuffer), dataUrl);
            var blob = upload.dataUrltoBlob(dataUrl, file.name);
            deferred.resolve(blob);
          } catch (e) {
            return deferred.reject(e);
          }
        };
        img.onerror = function () {
          deferred.reject();
        };
        img.src = url;
      }, function (e) {
        deferred.reject(e);
      });
    }, function (e) {
      deferred.reject(e);
    });
    return deferred.promise;
  };

  upload.restoreExif = function (orig, resized) {
    var ExifRestorer = {};

    ExifRestorer.KEY_STR = 'ABCDEFGHIJKLMNOP' +
      'QRSTUVWXYZabcdef' +
      'ghijklmnopqrstuv' +
      'wxyz0123456789+/' +
      '=';

    ExifRestorer.encode64 = function (input) {
      var output = '',
        chr1, chr2, chr3 = '',
        enc1, enc2, enc3, enc4 = '',
        i = 0;

      do {
        chr1 = input[i++];
        chr2 = input[i++];
        chr3 = input[i++];

        enc1 = chr1 >> 2;
        enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
        enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
        enc4 = chr3 & 63;

        if (isNaN(chr2)) {
          enc3 = enc4 = 64;
        } else if (isNaN(chr3)) {
          enc4 = 64;
        }

        output = output +
          this.KEY_STR.charAt(enc1) +
          this.KEY_STR.charAt(enc2) +
          this.KEY_STR.charAt(enc3) +
          this.KEY_STR.charAt(enc4);
        chr1 = chr2 = chr3 = '';
        enc1 = enc2 = enc3 = enc4 = '';
      } while (i < input.length);

      return output;
    };

    ExifRestorer.restore = function (origFileBase64, resizedFileBase64) {
      if (origFileBase64.match('data:image/jpeg;base64,')) {
        origFileBase64 = origFileBase64.replace('data:image/jpeg;base64,', '');
      }

      var rawImage = this.decode64(origFileBase64);
      var segments = this.slice2Segments(rawImage);

      var image = this.exifManipulation(resizedFileBase64, segments);

      return 'data:image/jpeg;base64,' + this.encode64(image);
    };


    ExifRestorer.exifManipulation = function (resizedFileBase64, segments) {
      var exifArray = this.getExifArray(segments),
        newImageArray = this.insertExif(resizedFileBase64, exifArray);
      return new Uint8Array(newImageArray);
    };


    ExifRestorer.getExifArray = function (segments) {
      var seg;
      for (var x = 0; x < segments.length; x++) {
        seg = segments[x];
        if (seg[0] === 255 & seg[1] === 225) //(ff e1)
        {
          return seg;
        }
      }
      return [];
    };


    ExifRestorer.insertExif = function (resizedFileBase64, exifArray) {
      var imageData = resizedFileBase64.replace('data:image/jpeg;base64,', ''),
        buf = this.decode64(imageData),
        separatePoint = buf.indexOf(255, 3),
        mae = buf.slice(0, separatePoint),
        ato = buf.slice(separatePoint),
        array = mae;

      array = array.concat(exifArray);
      array = array.concat(ato);
      return array;
    };


    ExifRestorer.slice2Segments = function (rawImageArray) {
      var head = 0,
        segments = [];

      while (1) {
        if (rawImageArray[head] === 255 & rawImageArray[head + 1] === 218) {
          break;
        }
        if (rawImageArray[head] === 255 & rawImageArray[head + 1] === 216) {
          head += 2;
        }
        else {
          var length = rawImageArray[head + 2] * 256 + rawImageArray[head + 3],
            endPoint = head + length + 2,
            seg = rawImageArray.slice(head, endPoint);
          segments.push(seg);
          head = endPoint;
        }
        if (head > rawImageArray.length) {
          break;
        }
      }

      return segments;
    };


    ExifRestorer.decode64 = function (input) {
      var chr1, chr2, chr3 = '',
        enc1, enc2, enc3, enc4 = '',
        i = 0,
        buf = [];

      // remove all characters that are not A-Z, a-z, 0-9, +, /, or =
      var base64test = /[^A-Za-z0-9\+\/\=]/g;
      if (base64test.exec(input)) {
        console.log('There were invalid base64 characters in the input text.\n' +
          'Valid base64 characters are A-Z, a-z, 0-9, ' + ', ' / ',and "="\n' +
          'Expect errors in decoding.');
      }
      input = input.replace(/[^A-Za-z0-9\+\/\=]/g, '');

      do {
        enc1 = this.KEY_STR.indexOf(input.charAt(i++));
        enc2 = this.KEY_STR.indexOf(input.charAt(i++));
        enc3 = this.KEY_STR.indexOf(input.charAt(i++));
        enc4 = this.KEY_STR.indexOf(input.charAt(i++));

        chr1 = (enc1 << 2) | (enc2 >> 4);
        chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
        chr3 = ((enc3 & 3) << 6) | enc4;

        buf.push(chr1);

        if (enc3 !== 64) {
          buf.push(chr2);
        }
        if (enc4 !== 64) {
          buf.push(chr3);
        }

        chr1 = chr2 = chr3 = '';
        enc1 = enc2 = enc3 = enc4 = '';

      } while (i < input.length);

      return buf;
    };

    return ExifRestorer.restore(orig, resized);  //<= EXIF
  };

  return upload;
}]);


(function (root) {
   "use strict";

/***** unorm.js *****/

/*
 * UnicodeNormalizer 1.0.0
 * Copyright (c) 2008 Matsuza
 * Dual licensed under the MIT (MIT-LICENSE.txt) and GPL (GPL-LICENSE.txt) licenses.
 * $Date: 2008-06-05 16:44:17 +0200 (Thu, 05 Jun 2008) $
 * $Rev: 13309 $
 */

   var DEFAULT_FEATURE = [null, 0, {}];
   var CACHE_THRESHOLD = 10;
   var SBase = 0xAC00, LBase = 0x1100, VBase = 0x1161, TBase = 0x11A7, LCount = 19, VCount = 21, TCount = 28;
   var NCount = VCount * TCount; // 588
   var SCount = LCount * NCount; // 11172

   var UChar = function(cp, feature){
      this.codepoint = cp;
      this.feature = feature;
   };

   // Strategies
   var cache = {};
   var cacheCounter = [];
   for (var i = 0; i <= 0xFF; ++i){
      cacheCounter[i] = 0;
   }

   function fromCache(next, cp, needFeature){
      var ret = cache[cp];
      if(!ret){
         ret = next(cp, needFeature);
         if(!!ret.feature && ++cacheCounter[(cp >> 8) & 0xFF] > CACHE_THRESHOLD){
            cache[cp] = ret;
         }
      }
      return ret;
   }

   function fromData(next, cp, needFeature){
      var hash = cp & 0xFF00;
      var dunit = UChar.udata[hash] || {};
      var f = dunit[cp];
      return f ? new UChar(cp, f) : new UChar(cp, DEFAULT_FEATURE);
   }
   function fromCpOnly(next, cp, needFeature){
      return !!needFeature ? next(cp, needFeature) : new UChar(cp, null);
   }
   function fromRuleBasedJamo(next, cp, needFeature){
      var j;
      if(cp < LBase || (LBase + LCount <= cp && cp < SBase) || (SBase + SCount < cp)){
         return next(cp, needFeature);
      }
      if(LBase <= cp && cp < LBase + LCount){
         var c = {};
         var base = (cp - LBase) * VCount;
         for (j = 0; j < VCount; ++j){
            c[VBase + j] = SBase + TCount * (j + base);
         }
         return new UChar(cp, [,,c]);
      }

      var SIndex = cp - SBase;
      var TIndex = SIndex % TCount;
      var feature = [];
      if(TIndex !== 0){
         feature[0] = [SBase + SIndex - TIndex, TBase + TIndex];
      } else {
         feature[0] = [LBase + Math.floor(SIndex / NCount), VBase + Math.floor((SIndex % NCount) / TCount)];
         feature[2] = {};
         for (j = 1; j < TCount; ++j){
            feature[2][TBase + j] = cp + j;
         }
      }
      return new UChar(cp, feature);
   }
   function fromCpFilter(next, cp, needFeature){
      return cp < 60 || 13311 < cp && cp < 42607 ? new UChar(cp, DEFAULT_FEATURE) : next(cp, needFeature);
   }

   var strategies = [fromCpFilter, fromCache, fromCpOnly, fromRuleBasedJamo, fromData];

   UChar.fromCharCode = strategies.reduceRight(function (next, strategy) {
      return function (cp, needFeature) {
         return strategy(next, cp, needFeature);
      };
   }, null);

   UChar.isHighSurrogate = function(cp){
      return cp >= 0xD800 && cp <= 0xDBFF;
   };
   UChar.isLowSurrogate = function(cp){
      return cp >= 0xDC00 && cp <= 0xDFFF;
   };

   UChar.prototype.prepFeature = function(){
      if(!this.feature){
         this.feature = UChar.fromCharCode(this.codepoint, true).feature;
      }
   };

   UChar.prototype.toString = function(){
      if(this.codepoint < 0x10000){
         return String.fromCharCode(this.codepoint);
      } else {
         var x = this.codepoint - 0x10000;
         return String.fromCharCode(Math.floor(x / 0x400) + 0xD800, x % 0x400 + 0xDC00);
      }
   };

   UChar.prototype.getDecomp = function(){
      this.prepFeature();
      return this.feature[0] || null;
   };

   UChar.prototype.isCompatibility = function(){
      this.prepFeature();
      return !!this.feature[1] && (this.feature[1] & (1 << 8));
   };
   UChar.prototype.isExclude = function(){
      this.prepFeature();
      return !!this.feature[1] && (this.feature[1] & (1 << 9));
   };
   UChar.prototype.getCanonicalClass = function(){
      this.prepFeature();
      return !!this.feature[1] ? (this.feature[1] & 0xff) : 0;
   };
   UChar.prototype.getComposite = function(following){
      this.prepFeature();
      if(!this.feature[2]){
         return null;
      }
      var cp = this.feature[2][following.codepoint];
      return cp ? UChar.fromCharCode(cp) : null;
   };

   var UCharIterator = function(str){
      this.str = str;
      this.cursor = 0;
   };
   UCharIterator.prototype.next = function(){
      if(!!this.str && this.cursor < this.str.length){
         var cp = this.str.charCodeAt(this.cursor++);
         var d;
         if(UChar.isHighSurrogate(cp) && this.cursor < this.str.length && UChar.isLowSurrogate((d = this.str.charCodeAt(this.cursor)))){
            cp = (cp - 0xD800) * 0x400 + (d -0xDC00) + 0x10000;
            ++this.cursor;
         }
         return UChar.fromCharCode(cp);
      } else {
         this.str = null;
         return null;
      }
   };

   var RecursDecompIterator = function(it, cano){
      this.it = it;
      this.canonical = cano;
      this.resBuf = [];
   };

   RecursDecompIterator.prototype.next = function(){
      function recursiveDecomp(cano, uchar){
         var decomp = uchar.getDecomp();
         if(!!decomp && !(cano && uchar.isCompatibility())){
            var ret = [];
            for(var i = 0; i < decomp.length; ++i){
               var a = recursiveDecomp(cano, UChar.fromCharCode(decomp[i]));
                ret = ret.concat(a);
            }
            return ret;
         } else {
            return [uchar];
         }
      }
      if(this.resBuf.length === 0){
         var uchar = this.it.next();
         if(!uchar){
            return null;
         }
         this.resBuf = recursiveDecomp(this.canonical, uchar);
      }
      return this.resBuf.shift();
   };

   var DecompIterator = function(it){
      this.it = it;
      this.resBuf = [];
   };

   DecompIterator.prototype.next = function(){
      var cc;
      if(this.resBuf.length === 0){
         do{
            var uchar = this.it.next();
            if(!uchar){
               break;
            }
            cc = uchar.getCanonicalClass();
            var inspt = this.resBuf.length;
            if(cc !== 0){
               for(; inspt > 0; --inspt){
                  var uchar2 = this.resBuf[inspt - 1];
                  var cc2 = uchar2.getCanonicalClass();
                  if(cc2 <= cc){
                     break;
                  }
               }
            }
            this.resBuf.splice(inspt, 0, uchar);
         } while(cc !== 0);
      }
      return this.resBuf.shift();
   };

   var CompIterator = function(it){
      this.it = it;
      this.procBuf = [];
      this.resBuf = [];
      this.lastClass = null;
   };

   CompIterator.prototype.next = function(){
      while(this.resBuf.length === 0){
         var uchar = this.it.next();
         if(!uchar){
            this.resBuf = this.procBuf;
            this.procBuf = [];
            break;
         }
         if(this.procBuf.length === 0){
            this.lastClass = uchar.getCanonicalClass();
            this.procBuf.push(uchar);
         } else {
            var starter = this.procBuf[0];
            var composite = starter.getComposite(uchar);
            var cc = uchar.getCanonicalClass();
            if(!!composite && (this.lastClass < cc || this.lastClass === 0)){
               this.procBuf[0] = composite;
            } else {
               if(cc === 0){
                  this.resBuf = this.procBuf;
                  this.procBuf = [];
               }
               this.lastClass = cc;
               this.procBuf.push(uchar);
            }
         }
      }
      return this.resBuf.shift();
   };

   var createIterator = function(mode, str){
      switch(mode){
         case "NFD":
            return new DecompIterator(new RecursDecompIterator(new UCharIterator(str), true));
         case "NFKD":
            return new DecompIterator(new RecursDecompIterator(new UCharIterator(str), false));
         case "NFC":
            return new CompIterator(new DecompIterator(new RecursDecompIterator(new UCharIterator(str), true)));
         case "NFKC":
            return new CompIterator(new DecompIterator(new RecursDecompIterator(new UCharIterator(str), false)));
      }
      throw mode + " is invalid";
   };
   var normalize = function(mode, str){
      var it = createIterator(mode, str);
      var ret = "";
      var uchar;
      while(!!(uchar = it.next())){
         ret += uchar.toString();
      }
      return ret;
   };

   /* API functions */
   function nfd(str){
      return normalize("NFD", str);
   }

   function nfkd(str){
      return normalize("NFKD", str);
   }

   function nfc(str){
      return normalize("NFC", str);
   }

   function nfkc(str){
      return normalize("NFKC", str);
   }

/* Unicode data */
UChar.udata={
0:{60:[,,{824:8814}],61:[,,{824:8800}],62:[,,{824:8815}],65:[,,{768:192,769:193,770:194,771:195,772:256,774:258,775:550,776:196,777:7842,778:197,780:461,783:512,785:514,803:7840,805:7680,808:260}],66:[,,{775:7682,803:7684,817:7686}],67:[,,{769:262,770:264,775:266,780:268,807:199}],68:[,,{775:7690,780:270,803:7692,807:7696,813:7698,817:7694}],69:[,,{768:200,769:201,770:202,771:7868,772:274,774:276,775:278,776:203,777:7866,780:282,783:516,785:518,803:7864,807:552,808:280,813:7704,816:7706}],70:[,,{775:7710}],71:[,,{769:500,770:284,772:7712,774:286,775:288,780:486,807:290}],72:[,,{770:292,775:7714,776:7718,780:542,803:7716,807:7720,814:7722}],73:[,,{768:204,769:205,770:206,771:296,772:298,774:300,775:304,776:207,777:7880,780:463,783:520,785:522,803:7882,808:302,816:7724}],74:[,,{770:308}],75:[,,{769:7728,780:488,803:7730,807:310,817:7732}],76:[,,{769:313,780:317,803:7734,807:315,813:7740,817:7738}],77:[,,{769:7742,775:7744,803:7746}],78:[,,{768:504,769:323,771:209,775:7748,780:327,803:7750,807:325,813:7754,817:7752}],79:[,,{768:210,769:211,770:212,771:213,772:332,774:334,775:558,776:214,777:7886,779:336,780:465,783:524,785:526,795:416,803:7884,808:490}],80:[,,{769:7764,775:7766}],82:[,,{769:340,775:7768,780:344,783:528,785:530,803:7770,807:342,817:7774}],83:[,,{769:346,770:348,775:7776,780:352,803:7778,806:536,807:350}],84:[,,{775:7786,780:356,803:7788,806:538,807:354,813:7792,817:7790}],85:[,,{768:217,769:218,770:219,771:360,772:362,774:364,776:220,777:7910,778:366,779:368,780:467,783:532,785:534,795:431,803:7908,804:7794,808:370,813:7798,816:7796}],86:[,,{771:7804,803:7806}],87:[,,{768:7808,769:7810,770:372,775:7814,776:7812,803:7816}],88:[,,{775:7818,776:7820}],89:[,,{768:7922,769:221,770:374,771:7928,772:562,775:7822,776:376,777:7926,803:7924}],90:[,,{769:377,770:7824,775:379,780:381,803:7826,817:7828}],97:[,,{768:224,769:225,770:226,771:227,772:257,774:259,775:551,776:228,777:7843,778:229,780:462,783:513,785:515,803:7841,805:7681,808:261}],98:[,,{775:7683,803:7685,817:7687}],99:[,,{769:263,770:265,775:267,780:269,807:231}],100:[,,{775:7691,780:271,803:7693,807:7697,813:7699,817:7695}],101:[,,{768:232,769:233,770:234,771:7869,772:275,774:277,775:279,776:235,777:7867,780:283,783:517,785:519,803:7865,807:553,808:281,813:7705,816:7707}],102:[,,{775:7711}],103:[,,{769:501,770:285,772:7713,774:287,775:289,780:487,807:291}],104:[,,{770:293,775:7715,776:7719,780:543,803:7717,807:7721,814:7723,817:7830}],105:[,,{768:236,769:237,770:238,771:297,772:299,774:301,776:239,777:7881,780:464,783:521,785:523,803:7883,808:303,816:7725}],106:[,,{770:309,780:496}],107:[,,{769:7729,780:489,803:7731,807:311,817:7733}],108:[,,{769:314,780:318,803:7735,807:316,813:7741,817:7739}],109:[,,{769:7743,775:7745,803:7747}],110:[,,{768:505,769:324,771:241,775:7749,780:328,803:7751,807:326,813:7755,817:7753}],111:[,,{768:242,769:243,770:244,771:245,772:333,774:335,775:559,776:246,777:7887,779:337,780:466,783:525,785:527,795:417,803:7885,808:491}],112:[,,{769:7765,775:7767}],114:[,,{769:341,775:7769,780:345,783:529,785:531,803:7771,807:343,817:7775}],115:[,,{769:347,770:349,775:7777,780:353,803:7779,806:537,807:351}],116:[,,{775:7787,776:7831,780:357,803:7789,806:539,807:355,813:7793,817:7791}],117:[,,{768:249,769:250,770:251,771:361,772:363,774:365,776:252,777:7911,778:367,779:369,780:468,783:533,785:535,795:432,803:7909,804:7795,808:371,813:7799,816:7797}],118:[,,{771:7805,803:7807}],119:[,,{768:7809,769:7811,770:373,775:7815,776:7813,778:7832,803:7817}],120:[,,{775:7819,776:7821}],121:[,,{768:7923,769:253,770:375,771:7929,772:563,775:7823,776:255,777:7927,778:7833,803:7925}],122:[,,{769:378,770:7825,775:380,780:382,803:7827,817:7829}],160:[[32],256],168:[[32,776],256,{768:8173,769:901,834:8129}],170:[[97],256],175:[[32,772],256],178:[[50],256],179:[[51],256],180:[[32,769],256],181:[[956],256],184:[[32,807],256],185:[[49],256],186:[[111],256],188:[[49,8260,52],256],189:[[49,8260,50],256],190:[[51,8260,52],256],192:[[65,768]],193:[[65,769]],194:[[65,770],,{768:7846,769:7844,771:7850,777:7848}],195:[[65,771]],196:[[65,776],,{772:478}],197:[[65,778],,{769:506}],198:[,,{769:508,772:482}],199:[[67,807],,{769:7688}],200:[[69,768]],201:[[69,769]],202:[[69,770],,{768:7872,769:7870,771:7876,777:7874}],203:[[69,776]],204:[[73,768]],205:[[73,769]],206:[[73,770]],207:[[73,776],,{769:7726}],209:[[78,771]],210:[[79,768]],211:[[79,769]],212:[[79,770],,{768:7890,769:7888,771:7894,777:7892}],213:[[79,771],,{769:7756,772:556,776:7758}],214:[[79,776],,{772:554}],216:[,,{769:510}],217:[[85,768]],218:[[85,769]],219:[[85,770]],220:[[85,776],,{768:475,769:471,772:469,780:473}],221:[[89,769]],224:[[97,768]],225:[[97,769]],226:[[97,770],,{768:7847,769:7845,771:7851,777:7849}],227:[[97,771]],228:[[97,776],,{772:479}],229:[[97,778],,{769:507}],230:[,,{769:509,772:483}],231:[[99,807],,{769:7689}],232:[[101,768]],233:[[101,769]],234:[[101,770],,{768:7873,769:7871,771:7877,777:7875}],235:[[101,776]],236:[[105,768]],237:[[105,769]],238:[[105,770]],239:[[105,776],,{769:7727}],241:[[110,771]],242:[[111,768]],243:[[111,769]],244:[[111,770],,{768:7891,769:7889,771:7895,777:7893}],245:[[111,771],,{769:7757,772:557,776:7759}],246:[[111,776],,{772:555}],248:[,,{769:511}],249:[[117,768]],250:[[117,769]],251:[[117,770]],252:[[117,776],,{768:476,769:472,772:470,780:474}],253:[[121,769]],255:[[121,776]]},
256:{256:[[65,772]],257:[[97,772]],258:[[65,774],,{768:7856,769:7854,771:7860,777:7858}],259:[[97,774],,{768:7857,769:7855,771:7861,777:7859}],260:[[65,808]],261:[[97,808]],262:[[67,769]],263:[[99,769]],264:[[67,770]],265:[[99,770]],266:[[67,775]],267:[[99,775]],268:[[67,780]],269:[[99,780]],270:[[68,780]],271:[[100,780]],274:[[69,772],,{768:7700,769:7702}],275:[[101,772],,{768:7701,769:7703}],276:[[69,774]],277:[[101,774]],278:[[69,775]],279:[[101,775]],280:[[69,808]],281:[[101,808]],282:[[69,780]],283:[[101,780]],284:[[71,770]],285:[[103,770]],286:[[71,774]],287:[[103,774]],288:[[71,775]],289:[[103,775]],290:[[71,807]],291:[[103,807]],292:[[72,770]],293:[[104,770]],296:[[73,771]],297:[[105,771]],298:[[73,772]],299:[[105,772]],300:[[73,774]],301:[[105,774]],302:[[73,808]],303:[[105,808]],304:[[73,775]],306:[[73,74],256],307:[[105,106],256],308:[[74,770]],309:[[106,770]],310:[[75,807]],311:[[107,807]],313:[[76,769]],314:[[108,769]],315:[[76,807]],316:[[108,807]],317:[[76,780]],318:[[108,780]],319:[[76,183],256],320:[[108,183],256],323:[[78,769]],324:[[110,769]],325:[[78,807]],326:[[110,807]],327:[[78,780]],328:[[110,780]],329:[[700,110],256],332:[[79,772],,{768:7760,769:7762}],333:[[111,772],,{768:7761,769:7763}],334:[[79,774]],335:[[111,774]],336:[[79,779]],337:[[111,779]],340:[[82,769]],341:[[114,769]],342:[[82,807]],343:[[114,807]],344:[[82,780]],345:[[114,780]],346:[[83,769],,{775:7780}],347:[[115,769],,{775:7781}],348:[[83,770]],349:[[115,770]],350:[[83,807]],351:[[115,807]],352:[[83,780],,{775:7782}],353:[[115,780],,{775:7783}],354:[[84,807]],355:[[116,807]],356:[[84,780]],357:[[116,780]],360:[[85,771],,{769:7800}],361:[[117,771],,{769:7801}],362:[[85,772],,{776:7802}],363:[[117,772],,{776:7803}],364:[[85,774]],365:[[117,774]],366:[[85,778]],367:[[117,778]],368:[[85,779]],369:[[117,779]],370:[[85,808]],371:[[117,808]],372:[[87,770]],373:[[119,770]],374:[[89,770]],375:[[121,770]],376:[[89,776]],377:[[90,769]],378:[[122,769]],379:[[90,775]],380:[[122,775]],381:[[90,780]],382:[[122,780]],383:[[115],256,{775:7835}],416:[[79,795],,{768:7900,769:7898,771:7904,777:7902,803:7906}],417:[[111,795],,{768:7901,769:7899,771:7905,777:7903,803:7907}],431:[[85,795],,{768:7914,769:7912,771:7918,777:7916,803:7920}],432:[[117,795],,{768:7915,769:7913,771:7919,777:7917,803:7921}],439:[,,{780:494}],452:[[68,381],256],453:[[68,382],256],454:[[100,382],256],455:[[76,74],256],456:[[76,106],256],457:[[108,106],256],458:[[78,74],256],459:[[78,106],256],460:[[110,106],256],461:[[65,780]],462:[[97,780]],463:[[73,780]],464:[[105,780]],465:[[79,780]],466:[[111,780]],467:[[85,780]],468:[[117,780]],469:[[220,772]],470:[[252,772]],471:[[220,769]],472:[[252,769]],473:[[220,780]],474:[[252,780]],475:[[220,768]],476:[[252,768]],478:[[196,772]],479:[[228,772]],480:[[550,772]],481:[[551,772]],482:[[198,772]],483:[[230,772]],486:[[71,780]],487:[[103,780]],488:[[75,780]],489:[[107,780]],490:[[79,808],,{772:492}],491:[[111,808],,{772:493}],492:[[490,772]],493:[[491,772]],494:[[439,780]],495:[[658,780]],496:[[106,780]],497:[[68,90],256],498:[[68,122],256],499:[[100,122],256],500:[[71,769]],501:[[103,769]],504:[[78,768]],505:[[110,768]],506:[[197,769]],507:[[229,769]],508:[[198,769]],509:[[230,769]],510:[[216,769]],511:[[248,769]],66045:[,220]},
512:{512:[[65,783]],513:[[97,783]],514:[[65,785]],515:[[97,785]],516:[[69,783]],517:[[101,783]],518:[[69,785]],519:[[101,785]],520:[[73,783]],521:[[105,783]],522:[[73,785]],523:[[105,785]],524:[[79,783]],525:[[111,783]],526:[[79,785]],527:[[111,785]],528:[[82,783]],529:[[114,783]],530:[[82,785]],531:[[114,785]],532:[[85,783]],533:[[117,783]],534:[[85,785]],535:[[117,785]],536:[[83,806]],537:[[115,806]],538:[[84,806]],539:[[116,806]],542:[[72,780]],543:[[104,780]],550:[[65,775],,{772:480}],551:[[97,775],,{772:481}],552:[[69,807],,{774:7708}],553:[[101,807],,{774:7709}],554:[[214,772]],555:[[246,772]],556:[[213,772]],557:[[245,772]],558:[[79,775],,{772:560}],559:[[111,775],,{772:561}],560:[[558,772]],561:[[559,772]],562:[[89,772]],563:[[121,772]],658:[,,{780:495}],688:[[104],256],689:[[614],256],690:[[106],256],691:[[114],256],692:[[633],256],693:[[635],256],694:[[641],256],695:[[119],256],696:[[121],256],728:[[32,774],256],729:[[32,775],256],730:[[32,778],256],731:[[32,808],256],732:[[32,771],256],733:[[32,779],256],736:[[611],256],737:[[108],256],738:[[115],256],739:[[120],256],740:[[661],256],66272:[,220]},
768:{768:[,230],769:[,230],770:[,230],771:[,230],772:[,230],773:[,230],774:[,230],775:[,230],776:[,230,{769:836}],777:[,230],778:[,230],779:[,230],780:[,230],781:[,230],782:[,230],783:[,230],784:[,230],785:[,230],786:[,230],787:[,230],788:[,230],789:[,232],790:[,220],791:[,220],792:[,220],793:[,220],794:[,232],795:[,216],796:[,220],797:[,220],798:[,220],799:[,220],800:[,220],801:[,202],802:[,202],803:[,220],804:[,220],805:[,220],806:[,220],807:[,202],808:[,202],809:[,220],810:[,220],811:[,220],812:[,220],813:[,220],814:[,220],815:[,220],816:[,220],817:[,220],818:[,220],819:[,220],820:[,1],821:[,1],822:[,1],823:[,1],824:[,1],825:[,220],826:[,220],827:[,220],828:[,220],829:[,230],830:[,230],831:[,230],832:[[768],230],833:[[769],230],834:[,230],835:[[787],230],836:[[776,769],230],837:[,240],838:[,230],839:[,220],840:[,220],841:[,220],842:[,230],843:[,230],844:[,230],845:[,220],846:[,220],848:[,230],849:[,230],850:[,230],851:[,220],852:[,220],853:[,220],854:[,220],855:[,230],856:[,232],857:[,220],858:[,220],859:[,230],860:[,233],861:[,234],862:[,234],863:[,233],864:[,234],865:[,234],866:[,233],867:[,230],868:[,230],869:[,230],870:[,230],871:[,230],872:[,230],873:[,230],874:[,230],875:[,230],876:[,230],877:[,230],878:[,230],879:[,230],884:[[697]],890:[[32,837],256],894:[[59]],900:[[32,769],256],901:[[168,769]],902:[[913,769]],903:[[183]],904:[[917,769]],905:[[919,769]],906:[[921,769]],908:[[927,769]],910:[[933,769]],911:[[937,769]],912:[[970,769]],913:[,,{768:8122,769:902,772:8121,774:8120,787:7944,788:7945,837:8124}],917:[,,{768:8136,769:904,787:7960,788:7961}],919:[,,{768:8138,769:905,787:7976,788:7977,837:8140}],921:[,,{768:8154,769:906,772:8153,774:8152,776:938,787:7992,788:7993}],927:[,,{768:8184,769:908,787:8008,788:8009}],929:[,,{788:8172}],933:[,,{768:8170,769:910,772:8169,774:8168,776:939,788:8025}],937:[,,{768:8186,769:911,787:8040,788:8041,837:8188}],938:[[921,776]],939:[[933,776]],940:[[945,769],,{837:8116}],941:[[949,769]],942:[[951,769],,{837:8132}],943:[[953,769]],944:[[971,769]],945:[,,{768:8048,769:940,772:8113,774:8112,787:7936,788:7937,834:8118,837:8115}],949:[,,{768:8050,769:941,787:7952,788:7953}],951:[,,{768:8052,769:942,787:7968,788:7969,834:8134,837:8131}],953:[,,{768:8054,769:943,772:8145,774:8144,776:970,787:7984,788:7985,834:8150}],959:[,,{768:8056,769:972,787:8000,788:8001}],961:[,,{787:8164,788:8165}],965:[,,{768:8058,769:973,772:8161,774:8160,776:971,787:8016,788:8017,834:8166}],969:[,,{768:8060,769:974,787:8032,788:8033,834:8182,837:8179}],970:[[953,776],,{768:8146,769:912,834:8151}],971:[[965,776],,{768:8162,769:944,834:8167}],972:[[959,769]],973:[[965,769]],974:[[969,769],,{837:8180}],976:[[946],256],977:[[952],256],978:[[933],256,{769:979,776:980}],979:[[978,769]],980:[[978,776]],981:[[966],256],982:[[960],256],1008:[[954],256],1009:[[961],256],1010:[[962],256],1012:[[920],256],1013:[[949],256],1017:[[931],256],66422:[,230],66423:[,230],66424:[,230],66425:[,230],66426:[,230]},
1024:{1024:[[1045,768]],1025:[[1045,776]],1027:[[1043,769]],1030:[,,{776:1031}],1031:[[1030,776]],1036:[[1050,769]],1037:[[1048,768]],1038:[[1059,774]],1040:[,,{774:1232,776:1234}],1043:[,,{769:1027}],1045:[,,{768:1024,774:1238,776:1025}],1046:[,,{774:1217,776:1244}],1047:[,,{776:1246}],1048:[,,{768:1037,772:1250,774:1049,776:1252}],1049:[[1048,774]],1050:[,,{769:1036}],1054:[,,{776:1254}],1059:[,,{772:1262,774:1038,776:1264,779:1266}],1063:[,,{776:1268}],1067:[,,{776:1272}],1069:[,,{776:1260}],1072:[,,{774:1233,776:1235}],1075:[,,{769:1107}],1077:[,,{768:1104,774:1239,776:1105}],1078:[,,{774:1218,776:1245}],1079:[,,{776:1247}],1080:[,,{768:1117,772:1251,774:1081,776:1253}],1081:[[1080,774]],1082:[,,{769:1116}],1086:[,,{776:1255}],1091:[,,{772:1263,774:1118,776:1265,779:1267}],1095:[,,{776:1269}],1099:[,,{776:1273}],1101:[,,{776:1261}],1104:[[1077,768]],1105:[[1077,776]],1107:[[1075,769]],1110:[,,{776:1111}],1111:[[1110,776]],1116:[[1082,769]],1117:[[1080,768]],1118:[[1091,774]],1140:[,,{783:1142}],1141:[,,{783:1143}],1142:[[1140,783]],1143:[[1141,783]],1155:[,230],1156:[,230],1157:[,230],1158:[,230],1159:[,230],1217:[[1046,774]],1218:[[1078,774]],1232:[[1040,774]],1233:[[1072,774]],1234:[[1040,776]],1235:[[1072,776]],1238:[[1045,774]],1239:[[1077,774]],1240:[,,{776:1242}],1241:[,,{776:1243}],1242:[[1240,776]],1243:[[1241,776]],1244:[[1046,776]],1245:[[1078,776]],1246:[[1047,776]],1247:[[1079,776]],1250:[[1048,772]],1251:[[1080,772]],1252:[[1048,776]],1253:[[1080,776]],1254:[[1054,776]],1255:[[1086,776]],1256:[,,{776:1258}],1257:[,,{776:1259}],1258:[[1256,776]],1259:[[1257,776]],1260:[[1069,776]],1261:[[1101,776]],1262:[[1059,772]],1263:[[1091,772]],1264:[[1059,776]],1265:[[1091,776]],1266:[[1059,779]],1267:[[1091,779]],1268:[[1063,776]],1269:[[1095,776]],1272:[[1067,776]],1273:[[1099,776]]},
1280:{1415:[[1381,1410],256],1425:[,220],1426:[,230],1427:[,230],1428:[,230],1429:[,230],1430:[,220],1431:[,230],1432:[,230],1433:[,230],1434:[,222],1435:[,220],1436:[,230],1437:[,230],1438:[,230],1439:[,230],1440:[,230],1441:[,230],1442:[,220],1443:[,220],1444:[,220],1445:[,220],1446:[,220],1447:[,220],1448:[,230],1449:[,230],1450:[,220],1451:[,230],1452:[,230],1453:[,222],1454:[,228],1455:[,230],1456:[,10],1457:[,11],1458:[,12],1459:[,13],1460:[,14],1461:[,15],1462:[,16],1463:[,17],1464:[,18],1465:[,19],1466:[,19],1467:[,20],1468:[,21],1469:[,22],1471:[,23],1473:[,24],1474:[,25],1476:[,230],1477:[,220],1479:[,18]},
1536:{1552:[,230],1553:[,230],1554:[,230],1555:[,230],1556:[,230],1557:[,230],1558:[,230],1559:[,230],1560:[,30],1561:[,31],1562:[,32],1570:[[1575,1619]],1571:[[1575,1620]],1572:[[1608,1620]],1573:[[1575,1621]],1574:[[1610,1620]],1575:[,,{1619:1570,1620:1571,1621:1573}],1608:[,,{1620:1572}],1610:[,,{1620:1574}],1611:[,27],1612:[,28],1613:[,29],1614:[,30],1615:[,31],1616:[,32],1617:[,33],1618:[,34],1619:[,230],1620:[,230],1621:[,220],1622:[,220],1623:[,230],1624:[,230],1625:[,230],1626:[,230],1627:[,230],1628:[,220],1629:[,230],1630:[,230],1631:[,220],1648:[,35],1653:[[1575,1652],256],1654:[[1608,1652],256],1655:[[1735,1652],256],1656:[[1610,1652],256],1728:[[1749,1620]],1729:[,,{1620:1730}],1730:[[1729,1620]],1746:[,,{1620:1747}],1747:[[1746,1620]],1749:[,,{1620:1728}],1750:[,230],1751:[,230],1752:[,230],1753:[,230],1754:[,230],1755:[,230],1756:[,230],1759:[,230],1760:[,230],1761:[,230],1762:[,230],1763:[,220],1764:[,230],1767:[,230],1768:[,230],1770:[,220],1771:[,230],1772:[,230],1773:[,220]},
1792:{1809:[,36],1840:[,230],1841:[,220],1842:[,230],1843:[,230],1844:[,220],1845:[,230],1846:[,230],1847:[,220],1848:[,220],1849:[,220],1850:[,230],1851:[,220],1852:[,220],1853:[,230],1854:[,220],1855:[,230],1856:[,230],1857:[,230],1858:[,220],1859:[,230],1860:[,220],1861:[,230],1862:[,220],1863:[,230],1864:[,220],1865:[,230],1866:[,230],2027:[,230],2028:[,230],2029:[,230],2030:[,230],2031:[,230],2032:[,230],2033:[,230],2034:[,220],2035:[,230]},
2048:{2070:[,230],2071:[,230],2072:[,230],2073:[,230],2075:[,230],2076:[,230],2077:[,230],2078:[,230],2079:[,230],2080:[,230],2081:[,230],2082:[,230],2083:[,230],2085:[,230],2086:[,230],2087:[,230],2089:[,230],2090:[,230],2091:[,230],2092:[,230],2093:[,230],2137:[,220],2138:[,220],2139:[,220],2276:[,230],2277:[,230],2278:[,220],2279:[,230],2280:[,230],2281:[,220],2282:[,230],2283:[,230],2284:[,230],2285:[,220],2286:[,220],2287:[,220],2288:[,27],2289:[,28],2290:[,29],2291:[,230],2292:[,230],2293:[,230],2294:[,220],2295:[,230],2296:[,230],2297:[,220],2298:[,220],2299:[,230],2300:[,230],2301:[,230],2302:[,230],2303:[,230]},
2304:{2344:[,,{2364:2345}],2345:[[2344,2364]],2352:[,,{2364:2353}],2353:[[2352,2364]],2355:[,,{2364:2356}],2356:[[2355,2364]],2364:[,7],2381:[,9],2385:[,230],2386:[,220],2387:[,230],2388:[,230],2392:[[2325,2364],512],2393:[[2326,2364],512],2394:[[2327,2364],512],2395:[[2332,2364],512],2396:[[2337,2364],512],2397:[[2338,2364],512],2398:[[2347,2364],512],2399:[[2351,2364],512],2492:[,7],2503:[,,{2494:2507,2519:2508}],2507:[[2503,2494]],2508:[[2503,2519]],2509:[,9],2524:[[2465,2492],512],2525:[[2466,2492],512],2527:[[2479,2492],512]},
2560:{2611:[[2610,2620],512],2614:[[2616,2620],512],2620:[,7],2637:[,9],2649:[[2582,2620],512],2650:[[2583,2620],512],2651:[[2588,2620],512],2654:[[2603,2620],512],2748:[,7],2765:[,9],68109:[,220],68111:[,230],68152:[,230],68153:[,1],68154:[,220],68159:[,9],68325:[,230],68326:[,220]},
2816:{2876:[,7],2887:[,,{2878:2891,2902:2888,2903:2892}],2888:[[2887,2902]],2891:[[2887,2878]],2892:[[2887,2903]],2893:[,9],2908:[[2849,2876],512],2909:[[2850,2876],512],2962:[,,{3031:2964}],2964:[[2962,3031]],3014:[,,{3006:3018,3031:3020}],3015:[,,{3006:3019}],3018:[[3014,3006]],3019:[[3015,3006]],3020:[[3014,3031]],3021:[,9]},
3072:{3142:[,,{3158:3144}],3144:[[3142,3158]],3149:[,9],3157:[,84],3158:[,91],3260:[,7],3263:[,,{3285:3264}],3264:[[3263,3285]],3270:[,,{3266:3274,3285:3271,3286:3272}],3271:[[3270,3285]],3272:[[3270,3286]],3274:[[3270,3266],,{3285:3275}],3275:[[3274,3285]],3277:[,9]},
3328:{3398:[,,{3390:3402,3415:3404}],3399:[,,{3390:3403}],3402:[[3398,3390]],3403:[[3399,3390]],3404:[[3398,3415]],3405:[,9],3530:[,9],3545:[,,{3530:3546,3535:3548,3551:3550}],3546:[[3545,3530]],3548:[[3545,3535],,{3530:3549}],3549:[[3548,3530]],3550:[[3545,3551]]},
3584:{3635:[[3661,3634],256],3640:[,103],3641:[,103],3642:[,9],3656:[,107],3657:[,107],3658:[,107],3659:[,107],3763:[[3789,3762],256],3768:[,118],3769:[,118],3784:[,122],3785:[,122],3786:[,122],3787:[,122],3804:[[3755,3737],256],3805:[[3755,3745],256]},
3840:{3852:[[3851],256],3864:[,220],3865:[,220],3893:[,220],3895:[,220],3897:[,216],3907:[[3906,4023],512],3917:[[3916,4023],512],3922:[[3921,4023],512],3927:[[3926,4023],512],3932:[[3931,4023],512],3945:[[3904,4021],512],3953:[,129],3954:[,130],3955:[[3953,3954],512],3956:[,132],3957:[[3953,3956],512],3958:[[4018,3968],512],3959:[[4018,3969],256],3960:[[4019,3968],512],3961:[[4019,3969],256],3962:[,130],3963:[,130],3964:[,130],3965:[,130],3968:[,130],3969:[[3953,3968],512],3970:[,230],3971:[,230],3972:[,9],3974:[,230],3975:[,230],3987:[[3986,4023],512],3997:[[3996,4023],512],4002:[[4001,4023],512],4007:[[4006,4023],512],4012:[[4011,4023],512],4025:[[3984,4021],512],4038:[,220]},
4096:{4133:[,,{4142:4134}],4134:[[4133,4142]],4151:[,7],4153:[,9],4154:[,9],4237:[,220],4348:[[4316],256],69702:[,9],69759:[,9],69785:[,,{69818:69786}],69786:[[69785,69818]],69787:[,,{69818:69788}],69788:[[69787,69818]],69797:[,,{69818:69803}],69803:[[69797,69818]],69817:[,9],69818:[,7]},
4352:{69888:[,230],69889:[,230],69890:[,230],69934:[[69937,69927]],69935:[[69938,69927]],69937:[,,{69927:69934}],69938:[,,{69927:69935}],69939:[,9],69940:[,9],70003:[,7],70080:[,9]},
4608:{70197:[,9],70198:[,7],70377:[,7],70378:[,9]},
4864:{4957:[,230],4958:[,230],4959:[,230],70460:[,7],70471:[,,{70462:70475,70487:70476}],70475:[[70471,70462]],70476:[[70471,70487]],70477:[,9],70502:[,230],70503:[,230],70504:[,230],70505:[,230],70506:[,230],70507:[,230],70508:[,230],70512:[,230],70513:[,230],70514:[,230],70515:[,230],70516:[,230]},
5120:{70841:[,,{70832:70844,70842:70843,70845:70846}],70843:[[70841,70842]],70844:[[70841,70832]],70846:[[70841,70845]],70850:[,9],70851:[,7]},
5376:{71096:[,,{71087:71098}],71097:[,,{71087:71099}],71098:[[71096,71087]],71099:[[71097,71087]],71103:[,9],71104:[,7]},
5632:{71231:[,9],71350:[,9],71351:[,7]},
5888:{5908:[,9],5940:[,9],6098:[,9],6109:[,230]},
6144:{6313:[,228]},
6400:{6457:[,222],6458:[,230],6459:[,220]},
6656:{6679:[,230],6680:[,220],6752:[,9],6773:[,230],6774:[,230],6775:[,230],6776:[,230],6777:[,230],6778:[,230],6779:[,230],6780:[,230],6783:[,220],6832:[,230],6833:[,230],6834:[,230],6835:[,230],6836:[,230],6837:[,220],6838:[,220],6839:[,220],6840:[,220],6841:[,220],6842:[,220],6843:[,230],6844:[,230],6845:[,220]},
6912:{6917:[,,{6965:6918}],6918:[[6917,6965]],6919:[,,{6965:6920}],6920:[[6919,6965]],6921:[,,{6965:6922}],6922:[[6921,6965]],6923:[,,{6965:6924}],6924:[[6923,6965]],6925:[,,{6965:6926}],6926:[[6925,6965]],6929:[,,{6965:6930}],6930:[[6929,6965]],6964:[,7],6970:[,,{6965:6971}],6971:[[6970,6965]],6972:[,,{6965:6973}],6973:[[6972,6965]],6974:[,,{6965:6976}],6975:[,,{6965:6977}],6976:[[6974,6965]],6977:[[6975,6965]],6978:[,,{6965:6979}],6979:[[6978,6965]],6980:[,9],7019:[,230],7020:[,220],7021:[,230],7022:[,230],7023:[,230],7024:[,230],7025:[,230],7026:[,230],7027:[,230],7082:[,9],7083:[,9],7142:[,7],7154:[,9],7155:[,9]},
7168:{7223:[,7],7376:[,230],7377:[,230],7378:[,230],7380:[,1],7381:[,220],7382:[,220],7383:[,220],7384:[,220],7385:[,220],7386:[,230],7387:[,230],7388:[,220],7389:[,220],7390:[,220],7391:[,220],7392:[,230],7394:[,1],7395:[,1],7396:[,1],7397:[,1],7398:[,1],7399:[,1],7400:[,1],7405:[,220],7412:[,230],7416:[,230],7417:[,230]},
7424:{7468:[[65],256],7469:[[198],256],7470:[[66],256],7472:[[68],256],7473:[[69],256],7474:[[398],256],7475:[[71],256],7476:[[72],256],7477:[[73],256],7478:[[74],256],7479:[[75],256],7480:[[76],256],7481:[[77],256],7482:[[78],256],7484:[[79],256],7485:[[546],256],7486:[[80],256],7487:[[82],256],7488:[[84],256],7489:[[85],256],7490:[[87],256],7491:[[97],256],7492:[[592],256],7493:[[593],256],7494:[[7426],256],7495:[[98],256],7496:[[100],256],7497:[[101],256],7498:[[601],256],7499:[[603],256],7500:[[604],256],7501:[[103],256],7503:[[107],256],7504:[[109],256],7505:[[331],256],7506:[[111],256],7507:[[596],256],7508:[[7446],256],7509:[[7447],256],7510:[[112],256],7511:[[116],256],7512:[[117],256],7513:[[7453],256],7514:[[623],256],7515:[[118],256],7516:[[7461],256],7517:[[946],256],7518:[[947],256],7519:[[948],256],7520:[[966],256],7521:[[967],256],7522:[[105],256],7523:[[114],256],7524:[[117],256],7525:[[118],256],7526:[[946],256],7527:[[947],256],7528:[[961],256],7529:[[966],256],7530:[[967],256],7544:[[1085],256],7579:[[594],256],7580:[[99],256],7581:[[597],256],7582:[[240],256],7583:[[604],256],7584:[[102],256],7585:[[607],256],7586:[[609],256],7587:[[613],256],7588:[[616],256],7589:[[617],256],7590:[[618],256],7591:[[7547],256],7592:[[669],256],7593:[[621],256],7594:[[7557],256],7595:[[671],256],7596:[[625],256],7597:[[624],256],7598:[[626],256],7599:[[627],256],7600:[[628],256],7601:[[629],256],7602:[[632],256],7603:[[642],256],7604:[[643],256],7605:[[427],256],7606:[[649],256],7607:[[650],256],7608:[[7452],256],7609:[[651],256],7610:[[652],256],7611:[[122],256],7612:[[656],256],7613:[[657],256],7614:[[658],256],7615:[[952],256],7616:[,230],7617:[,230],7618:[,220],7619:[,230],7620:[,230],7621:[,230],7622:[,230],7623:[,230],7624:[,230],7625:[,230],7626:[,220],7627:[,230],7628:[,230],7629:[,234],7630:[,214],7631:[,220],7632:[,202],7633:[,230],7634:[,230],7635:[,230],7636:[,230],7637:[,230],7638:[,230],7639:[,230],7640:[,230],7641:[,230],7642:[,230],7643:[,230],7644:[,230],7645:[,230],7646:[,230],7647:[,230],7648:[,230],7649:[,230],7650:[,230],7651:[,230],7652:[,230],7653:[,230],7654:[,230],7655:[,230],7656:[,230],7657:[,230],7658:[,230],7659:[,230],7660:[,230],7661:[,230],7662:[,230],7663:[,230],7664:[,230],7665:[,230],7666:[,230],7667:[,230],7668:[,230],7669:[,230],7676:[,233],7677:[,220],7678:[,230],7679:[,220]},
7680:{7680:[[65,805]],7681:[[97,805]],7682:[[66,775]],7683:[[98,775]],7684:[[66,803]],7685:[[98,803]],7686:[[66,817]],7687:[[98,817]],7688:[[199,769]],7689:[[231,769]],7690:[[68,775]],7691:[[100,775]],7692:[[68,803]],7693:[[100,803]],7694:[[68,817]],7695:[[100,817]],7696:[[68,807]],7697:[[100,807]],7698:[[68,813]],7699:[[100,813]],7700:[[274,768]],7701:[[275,768]],7702:[[274,769]],7703:[[275,769]],7704:[[69,813]],7705:[[101,813]],7706:[[69,816]],7707:[[101,816]],7708:[[552,774]],7709:[[553,774]],7710:[[70,775]],7711:[[102,775]],7712:[[71,772]],7713:[[103,772]],7714:[[72,775]],7715:[[104,775]],7716:[[72,803]],7717:[[104,803]],7718:[[72,776]],7719:[[104,776]],7720:[[72,807]],7721:[[104,807]],7722:[[72,814]],7723:[[104,814]],7724:[[73,816]],7725:[[105,816]],7726:[[207,769]],7727:[[239,769]],7728:[[75,769]],7729:[[107,769]],7730:[[75,803]],7731:[[107,803]],7732:[[75,817]],7733:[[107,817]],7734:[[76,803],,{772:7736}],7735:[[108,803],,{772:7737}],7736:[[7734,772]],7737:[[7735,772]],7738:[[76,817]],7739:[[108,817]],7740:[[76,813]],7741:[[108,813]],7742:[[77,769]],7743:[[109,769]],7744:[[77,775]],7745:[[109,775]],7746:[[77,803]],7747:[[109,803]],7748:[[78,775]],7749:[[110,775]],7750:[[78,803]],7751:[[110,803]],7752:[[78,817]],7753:[[110,817]],7754:[[78,813]],7755:[[110,813]],7756:[[213,769]],7757:[[245,769]],7758:[[213,776]],7759:[[245,776]],7760:[[332,768]],7761:[[333,768]],7762:[[332,769]],7763:[[333,769]],7764:[[80,769]],7765:[[112,769]],7766:[[80,775]],7767:[[112,775]],7768:[[82,775]],7769:[[114,775]],7770:[[82,803],,{772:7772}],7771:[[114,803],,{772:7773}],7772:[[7770,772]],7773:[[7771,772]],7774:[[82,817]],7775:[[114,817]],7776:[[83,775]],7777:[[115,775]],7778:[[83,803],,{775:7784}],7779:[[115,803],,{775:7785}],7780:[[346,775]],7781:[[347,775]],7782:[[352,775]],7783:[[353,775]],7784:[[7778,775]],7785:[[7779,775]],7786:[[84,775]],7787:[[116,775]],7788:[[84,803]],7789:[[116,803]],7790:[[84,817]],7791:[[116,817]],7792:[[84,813]],7793:[[116,813]],7794:[[85,804]],7795:[[117,804]],7796:[[85,816]],7797:[[117,816]],7798:[[85,813]],7799:[[117,813]],7800:[[360,769]],7801:[[361,769]],7802:[[362,776]],7803:[[363,776]],7804:[[86,771]],7805:[[118,771]],7806:[[86,803]],7807:[[118,803]],7808:[[87,768]],7809:[[119,768]],7810:[[87,769]],7811:[[119,769]],7812:[[87,776]],7813:[[119,776]],7814:[[87,775]],7815:[[119,775]],7816:[[87,803]],7817:[[119,803]],7818:[[88,775]],7819:[[120,775]],7820:[[88,776]],7821:[[120,776]],7822:[[89,775]],7823:[[121,775]],7824:[[90,770]],7825:[[122,770]],7826:[[90,803]],7827:[[122,803]],7828:[[90,817]],7829:[[122,817]],7830:[[104,817]],7831:[[116,776]],7832:[[119,778]],7833:[[121,778]],7834:[[97,702],256],7835:[[383,775]],7840:[[65,803],,{770:7852,774:7862}],7841:[[97,803],,{770:7853,774:7863}],7842:[[65,777]],7843:[[97,777]],7844:[[194,769]],7845:[[226,769]],7846:[[194,768]],7847:[[226,768]],7848:[[194,777]],7849:[[226,777]],7850:[[194,771]],7851:[[226,771]],7852:[[7840,770]],7853:[[7841,770]],7854:[[258,769]],7855:[[259,769]],7856:[[258,768]],7857:[[259,768]],7858:[[258,777]],7859:[[259,777]],7860:[[258,771]],7861:[[259,771]],7862:[[7840,774]],7863:[[7841,774]],7864:[[69,803],,{770:7878}],7865:[[101,803],,{770:7879}],7866:[[69,777]],7867:[[101,777]],7868:[[69,771]],7869:[[101,771]],7870:[[202,769]],7871:[[234,769]],7872:[[202,768]],7873:[[234,768]],7874:[[202,777]],7875:[[234,777]],7876:[[202,771]],7877:[[234,771]],7878:[[7864,770]],7879:[[7865,770]],7880:[[73,777]],7881:[[105,777]],7882:[[73,803]],7883:[[105,803]],7884:[[79,803],,{770:7896}],7885:[[111,803],,{770:7897}],7886:[[79,777]],7887:[[111,777]],7888:[[212,769]],7889:[[244,769]],7890:[[212,768]],7891:[[244,768]],7892:[[212,777]],7893:[[244,777]],7894:[[212,771]],7895:[[244,771]],7896:[[7884,770]],7897:[[7885,770]],7898:[[416,769]],7899:[[417,769]],7900:[[416,768]],7901:[[417,768]],7902:[[416,777]],7903:[[417,777]],7904:[[416,771]],7905:[[417,771]],7906:[[416,803]],7907:[[417,803]],7908:[[85,803]],7909:[[117,803]],7910:[[85,777]],7911:[[117,777]],7912:[[431,769]],7913:[[432,769]],7914:[[431,768]],7915:[[432,768]],7916:[[431,777]],7917:[[432,777]],7918:[[431,771]],7919:[[432,771]],7920:[[431,803]],7921:[[432,803]],7922:[[89,768]],7923:[[121,768]],7924:[[89,803]],7925:[[121,803]],7926:[[89,777]],7927:[[121,777]],7928:[[89,771]],7929:[[121,771]]},
7936:{7936:[[945,787],,{768:7938,769:7940,834:7942,837:8064}],7937:[[945,788],,{768:7939,769:7941,834:7943,837:8065}],7938:[[7936,768],,{837:8066}],7939:[[7937,768],,{837:8067}],7940:[[7936,769],,{837:8068}],7941:[[7937,769],,{837:8069}],7942:[[7936,834],,{837:8070}],7943:[[7937,834],,{837:8071}],7944:[[913,787],,{768:7946,769:7948,834:7950,837:8072}],7945:[[913,788],,{768:7947,769:7949,834:7951,837:8073}],7946:[[7944,768],,{837:8074}],7947:[[7945,768],,{837:8075}],7948:[[7944,769],,{837:8076}],7949:[[7945,769],,{837:8077}],7950:[[7944,834],,{837:8078}],7951:[[7945,834],,{837:8079}],7952:[[949,787],,{768:7954,769:7956}],7953:[[949,788],,{768:7955,769:7957}],7954:[[7952,768]],7955:[[7953,768]],7956:[[7952,769]],7957:[[7953,769]],7960:[[917,787],,{768:7962,769:7964}],7961:[[917,788],,{768:7963,769:7965}],7962:[[7960,768]],7963:[[7961,768]],7964:[[7960,769]],7965:[[7961,769]],7968:[[951,787],,{768:7970,769:7972,834:7974,837:8080}],7969:[[951,788],,{768:7971,769:7973,834:7975,837:8081}],7970:[[7968,768],,{837:8082}],7971:[[7969,768],,{837:8083}],7972:[[7968,769],,{837:8084}],7973:[[7969,769],,{837:8085}],7974:[[7968,834],,{837:8086}],7975:[[7969,834],,{837:8087}],7976:[[919,787],,{768:7978,769:7980,834:7982,837:8088}],7977:[[919,788],,{768:7979,769:7981,834:7983,837:8089}],7978:[[7976,768],,{837:8090}],7979:[[7977,768],,{837:8091}],7980:[[7976,769],,{837:8092}],7981:[[7977,769],,{837:8093}],7982:[[7976,834],,{837:8094}],7983:[[7977,834],,{837:8095}],7984:[[953,787],,{768:7986,769:7988,834:7990}],7985:[[953,788],,{768:7987,769:7989,834:7991}],7986:[[7984,768]],7987:[[7985,768]],7988:[[7984,769]],7989:[[7985,769]],7990:[[7984,834]],7991:[[7985,834]],7992:[[921,787],,{768:7994,769:7996,834:7998}],7993:[[921,788],,{768:7995,769:7997,834:7999}],7994:[[7992,768]],7995:[[7993,768]],7996:[[7992,769]],7997:[[7993,769]],7998:[[7992,834]],7999:[[7993,834]],8000:[[959,787],,{768:8002,769:8004}],8001:[[959,788],,{768:8003,769:8005}],8002:[[8000,768]],8003:[[8001,768]],8004:[[8000,769]],8005:[[8001,769]],8008:[[927,787],,{768:8010,769:8012}],8009:[[927,788],,{768:8011,769:8013}],8010:[[8008,768]],8011:[[8009,768]],8012:[[8008,769]],8013:[[8009,769]],8016:[[965,787],,{768:8018,769:8020,834:8022}],8017:[[965,788],,{768:8019,769:8021,834:8023}],8018:[[8016,768]],8019:[[8017,768]],8020:[[8016,769]],8021:[[8017,769]],8022:[[8016,834]],8023:[[8017,834]],8025:[[933,788],,{768:8027,769:8029,834:8031}],8027:[[8025,768]],8029:[[8025,769]],8031:[[8025,834]],8032:[[969,787],,{768:8034,769:8036,834:8038,837:8096}],8033:[[969,788],,{768:8035,769:8037,834:8039,837:8097}],8034:[[8032,768],,{837:8098}],8035:[[8033,768],,{837:8099}],8036:[[8032,769],,{837:8100}],8037:[[8033,769],,{837:8101}],8038:[[8032,834],,{837:8102}],8039:[[8033,834],,{837:8103}],8040:[[937,787],,{768:8042,769:8044,834:8046,837:8104}],8041:[[937,788],,{768:8043,769:8045,834:8047,837:8105}],8042:[[8040,768],,{837:8106}],8043:[[8041,768],,{837:8107}],8044:[[8040,769],,{837:8108}],8045:[[8041,769],,{837:8109}],8046:[[8040,834],,{837:8110}],8047:[[8041,834],,{837:8111}],8048:[[945,768],,{837:8114}],8049:[[940]],8050:[[949,768]],8051:[[941]],8052:[[951,768],,{837:8130}],8053:[[942]],8054:[[953,768]],8055:[[943]],8056:[[959,768]],8057:[[972]],8058:[[965,768]],8059:[[973]],8060:[[969,768],,{837:8178}],8061:[[974]],8064:[[7936,837]],8065:[[7937,837]],8066:[[7938,837]],8067:[[7939,837]],8068:[[7940,837]],8069:[[7941,837]],8070:[[7942,837]],8071:[[7943,837]],8072:[[7944,837]],8073:[[7945,837]],8074:[[7946,837]],8075:[[7947,837]],8076:[[7948,837]],8077:[[7949,837]],8078:[[7950,837]],8079:[[7951,837]],8080:[[7968,837]],8081:[[7969,837]],8082:[[7970,837]],8083:[[7971,837]],8084:[[7972,837]],8085:[[7973,837]],8086:[[7974,837]],8087:[[7975,837]],8088:[[7976,837]],8089:[[7977,837]],8090:[[7978,837]],8091:[[7979,837]],8092:[[7980,837]],8093:[[7981,837]],8094:[[7982,837]],8095:[[7983,837]],8096:[[8032,837]],8097:[[8033,837]],8098:[[8034,837]],8099:[[8035,837]],8100:[[8036,837]],8101:[[8037,837]],8102:[[8038,837]],8103:[[8039,837]],8104:[[8040,837]],8105:[[8041,837]],8106:[[8042,837]],8107:[[8043,837]],8108:[[8044,837]],8109:[[8045,837]],8110:[[8046,837]],8111:[[8047,837]],8112:[[945,774]],8113:[[945,772]],8114:[[8048,837]],8115:[[945,837]],8116:[[940,837]],8118:[[945,834],,{837:8119}],8119:[[8118,837]],8120:[[913,774]],8121:[[913,772]],8122:[[913,768]],8123:[[902]],8124:[[913,837]],8125:[[32,787],256],8126:[[953]],8127:[[32,787],256,{768:8141,769:8142,834:8143}],8128:[[32,834],256],8129:[[168,834]],8130:[[8052,837]],8131:[[951,837]],8132:[[942,837]],8134:[[951,834],,{837:8135}],8135:[[8134,837]],8136:[[917,768]],8137:[[904]],8138:[[919,768]],8139:[[905]],8140:[[919,837]],8141:[[8127,768]],8142:[[8127,769]],8143:[[8127,834]],8144:[[953,774]],8145:[[953,772]],8146:[[970,768]],8147:[[912]],8150:[[953,834]],8151:[[970,834]],8152:[[921,774]],8153:[[921,772]],8154:[[921,768]],8155:[[906]],8157:[[8190,768]],8158:[[8190,769]],8159:[[8190,834]],8160:[[965,774]],8161:[[965,772]],8162:[[971,768]],8163:[[944]],8164:[[961,787]],8165:[[961,788]],8166:[[965,834]],8167:[[971,834]],8168:[[933,774]],8169:[[933,772]],8170:[[933,768]],8171:[[910]],8172:[[929,788]],8173:[[168,768]],8174:[[901]],8175:[[96]],8178:[[8060,837]],8179:[[969,837]],8180:[[974,837]],8182:[[969,834],,{837:8183}],8183:[[8182,837]],8184:[[927,768]],8185:[[908]],8186:[[937,768]],8187:[[911]],8188:[[937,837]],8189:[[180]],8190:[[32,788],256,{768:8157,769:8158,834:8159}]},
8192:{8192:[[8194]],8193:[[8195]],8194:[[32],256],8195:[[32],256],8196:[[32],256],8197:[[32],256],8198:[[32],256],8199:[[32],256],8200:[[32],256],8201:[[32],256],8202:[[32],256],8209:[[8208],256],8215:[[32,819],256],8228:[[46],256],8229:[[46,46],256],8230:[[46,46,46],256],8239:[[32],256],8243:[[8242,8242],256],8244:[[8242,8242,8242],256],8246:[[8245,8245],256],8247:[[8245,8245,8245],256],8252:[[33,33],256],8254:[[32,773],256],8263:[[63,63],256],8264:[[63,33],256],8265:[[33,63],256],8279:[[8242,8242,8242,8242],256],8287:[[32],256],8304:[[48],256],8305:[[105],256],8308:[[52],256],8309:[[53],256],8310:[[54],256],8311:[[55],256],8312:[[56],256],8313:[[57],256],8314:[[43],256],8315:[[8722],256],8316:[[61],256],8317:[[40],256],8318:[[41],256],8319:[[110],256],8320:[[48],256],8321:[[49],256],8322:[[50],256],8323:[[51],256],8324:[[52],256],8325:[[53],256],8326:[[54],256],8327:[[55],256],8328:[[56],256],8329:[[57],256],8330:[[43],256],8331:[[8722],256],8332:[[61],256],8333:[[40],256],8334:[[41],256],8336:[[97],256],8337:[[101],256],8338:[[111],256],8339:[[120],256],8340:[[601],256],8341:[[104],256],8342:[[107],256],8343:[[108],256],8344:[[109],256],8345:[[110],256],8346:[[112],256],8347:[[115],256],8348:[[116],256],8360:[[82,115],256],8400:[,230],8401:[,230],8402:[,1],8403:[,1],8404:[,230],8405:[,230],8406:[,230],8407:[,230],8408:[,1],8409:[,1],8410:[,1],8411:[,230],8412:[,230],8417:[,230],8421:[,1],8422:[,1],8423:[,230],8424:[,220],8425:[,230],8426:[,1],8427:[,1],8428:[,220],8429:[,220],8430:[,220],8431:[,220],8432:[,230]},
8448:{8448:[[97,47,99],256],8449:[[97,47,115],256],8450:[[67],256],8451:[[176,67],256],8453:[[99,47,111],256],8454:[[99,47,117],256],8455:[[400],256],8457:[[176,70],256],8458:[[103],256],8459:[[72],256],8460:[[72],256],8461:[[72],256],8462:[[104],256],8463:[[295],256],8464:[[73],256],8465:[[73],256],8466:[[76],256],8467:[[108],256],8469:[[78],256],8470:[[78,111],256],8473:[[80],256],8474:[[81],256],8475:[[82],256],8476:[[82],256],8477:[[82],256],8480:[[83,77],256],8481:[[84,69,76],256],8482:[[84,77],256],8484:[[90],256],8486:[[937]],8488:[[90],256],8490:[[75]],8491:[[197]],8492:[[66],256],8493:[[67],256],8495:[[101],256],8496:[[69],256],8497:[[70],256],8499:[[77],256],8500:[[111],256],8501:[[1488],256],8502:[[1489],256],8503:[[1490],256],8504:[[1491],256],8505:[[105],256],8507:[[70,65,88],256],8508:[[960],256],8509:[[947],256],8510:[[915],256],8511:[[928],256],8512:[[8721],256],8517:[[68],256],8518:[[100],256],8519:[[101],256],8520:[[105],256],8521:[[106],256],8528:[[49,8260,55],256],8529:[[49,8260,57],256],8530:[[49,8260,49,48],256],8531:[[49,8260,51],256],8532:[[50,8260,51],256],8533:[[49,8260,53],256],8534:[[50,8260,53],256],8535:[[51,8260,53],256],8536:[[52,8260,53],256],8537:[[49,8260,54],256],8538:[[53,8260,54],256],8539:[[49,8260,56],256],8540:[[51,8260,56],256],8541:[[53,8260,56],256],8542:[[55,8260,56],256],8543:[[49,8260],256],8544:[[73],256],8545:[[73,73],256],8546:[[73,73,73],256],8547:[[73,86],256],8548:[[86],256],8549:[[86,73],256],8550:[[86,73,73],256],8551:[[86,73,73,73],256],8552:[[73,88],256],8553:[[88],256],8554:[[88,73],256],8555:[[88,73,73],256],8556:[[76],256],8557:[[67],256],8558:[[68],256],8559:[[77],256],8560:[[105],256],8561:[[105,105],256],8562:[[105,105,105],256],8563:[[105,118],256],8564:[[118],256],8565:[[118,105],256],8566:[[118,105,105],256],8567:[[118,105,105,105],256],8568:[[105,120],256],8569:[[120],256],8570:[[120,105],256],8571:[[120,105,105],256],8572:[[108],256],8573:[[99],256],8574:[[100],256],8575:[[109],256],8585:[[48,8260,51],256],8592:[,,{824:8602}],8594:[,,{824:8603}],8596:[,,{824:8622}],8602:[[8592,824]],8603:[[8594,824]],8622:[[8596,824]],8653:[[8656,824]],8654:[[8660,824]],8655:[[8658,824]],8656:[,,{824:8653}],8658:[,,{824:8655}],8660:[,,{824:8654}]},
8704:{8707:[,,{824:8708}],8708:[[8707,824]],8712:[,,{824:8713}],8713:[[8712,824]],8715:[,,{824:8716}],8716:[[8715,824]],8739:[,,{824:8740}],8740:[[8739,824]],8741:[,,{824:8742}],8742:[[8741,824]],8748:[[8747,8747],256],8749:[[8747,8747,8747],256],8751:[[8750,8750],256],8752:[[8750,8750,8750],256],8764:[,,{824:8769}],8769:[[8764,824]],8771:[,,{824:8772}],8772:[[8771,824]],8773:[,,{824:8775}],8775:[[8773,824]],8776:[,,{824:8777}],8777:[[8776,824]],8781:[,,{824:8813}],8800:[[61,824]],8801:[,,{824:8802}],8802:[[8801,824]],8804:[,,{824:8816}],8805:[,,{824:8817}],8813:[[8781,824]],8814:[[60,824]],8815:[[62,824]],8816:[[8804,824]],8817:[[8805,824]],8818:[,,{824:8820}],8819:[,,{824:8821}],8820:[[8818,824]],8821:[[8819,824]],8822:[,,{824:8824}],8823:[,,{824:8825}],8824:[[8822,824]],8825:[[8823,824]],8826:[,,{824:8832}],8827:[,,{824:8833}],8828:[,,{824:8928}],8829:[,,{824:8929}],8832:[[8826,824]],8833:[[8827,824]],8834:[,,{824:8836}],8835:[,,{824:8837}],8836:[[8834,824]],8837:[[8835,824]],8838:[,,{824:8840}],8839:[,,{824:8841}],8840:[[8838,824]],8841:[[8839,824]],8849:[,,{824:8930}],8850:[,,{824:8931}],8866:[,,{824:8876}],8872:[,,{824:8877}],8873:[,,{824:8878}],8875:[,,{824:8879}],8876:[[8866,824]],8877:[[8872,824]],8878:[[8873,824]],8879:[[8875,824]],8882:[,,{824:8938}],8883:[,,{824:8939}],8884:[,,{824:8940}],8885:[,,{824:8941}],8928:[[8828,824]],8929:[[8829,824]],8930:[[8849,824]],8931:[[8850,824]],8938:[[8882,824]],8939:[[8883,824]],8940:[[8884,824]],8941:[[8885,824]]},
8960:{9001:[[12296]],9002:[[12297]]},
9216:{9312:[[49],256],9313:[[50],256],9314:[[51],256],9315:[[52],256],9316:[[53],256],9317:[[54],256],9318:[[55],256],9319:[[56],256],9320:[[57],256],9321:[[49,48],256],9322:[[49,49],256],9323:[[49,50],256],9324:[[49,51],256],9325:[[49,52],256],9326:[[49,53],256],9327:[[49,54],256],9328:[[49,55],256],9329:[[49,56],256],9330:[[49,57],256],9331:[[50,48],256],9332:[[40,49,41],256],9333:[[40,50,41],256],9334:[[40,51,41],256],9335:[[40,52,41],256],9336:[[40,53,41],256],9337:[[40,54,41],256],9338:[[40,55,41],256],9339:[[40,56,41],256],9340:[[40,57,41],256],9341:[[40,49,48,41],256],9342:[[40,49,49,41],256],9343:[[40,49,50,41],256],9344:[[40,49,51,41],256],9345:[[40,49,52,41],256],9346:[[40,49,53,41],256],9347:[[40,49,54,41],256],9348:[[40,49,55,41],256],9349:[[40,49,56,41],256],9350:[[40,49,57,41],256],9351:[[40,50,48,41],256],9352:[[49,46],256],9353:[[50,46],256],9354:[[51,46],256],9355:[[52,46],256],9356:[[53,46],256],9357:[[54,46],256],9358:[[55,46],256],9359:[[56,46],256],9360:[[57,46],256],9361:[[49,48,46],256],9362:[[49,49,46],256],9363:[[49,50,46],256],9364:[[49,51,46],256],9365:[[49,52,46],256],9366:[[49,53,46],256],9367:[[49,54,46],256],9368:[[49,55,46],256],9369:[[49,56,46],256],9370:[[49,57,46],256],9371:[[50,48,46],256],9372:[[40,97,41],256],9373:[[40,98,41],256],9374:[[40,99,41],256],9375:[[40,100,41],256],9376:[[40,101,41],256],9377:[[40,102,41],256],9378:[[40,103,41],256],9379:[[40,104,41],256],9380:[[40,105,41],256],9381:[[40,106,41],256],9382:[[40,107,41],256],9383:[[40,108,41],256],9384:[[40,109,41],256],9385:[[40,110,41],256],9386:[[40,111,41],256],9387:[[40,112,41],256],9388:[[40,113,41],256],9389:[[40,114,41],256],9390:[[40,115,41],256],9391:[[40,116,41],256],9392:[[40,117,41],256],9393:[[40,118,41],256],9394:[[40,119,41],256],9395:[[40,120,41],256],9396:[[40,121,41],256],9397:[[40,122,41],256],9398:[[65],256],9399:[[66],256],9400:[[67],256],9401:[[68],256],9402:[[69],256],9403:[[70],256],9404:[[71],256],9405:[[72],256],9406:[[73],256],9407:[[74],256],9408:[[75],256],9409:[[76],256],9410:[[77],256],9411:[[78],256],9412:[[79],256],9413:[[80],256],9414:[[81],256],9415:[[82],256],9416:[[83],256],9417:[[84],256],9418:[[85],256],9419:[[86],256],9420:[[87],256],9421:[[88],256],9422:[[89],256],9423:[[90],256],9424:[[97],256],9425:[[98],256],9426:[[99],256],9427:[[100],256],9428:[[101],256],9429:[[102],256],9430:[[103],256],9431:[[104],256],9432:[[105],256],9433:[[106],256],9434:[[107],256],9435:[[108],256],9436:[[109],256],9437:[[110],256],9438:[[111],256],9439:[[112],256],9440:[[113],256],9441:[[114],256],9442:[[115],256],9443:[[116],256],9444:[[117],256],9445:[[118],256],9446:[[119],256],9447:[[120],256],9448:[[121],256],9449:[[122],256],9450:[[48],256]},
10752:{10764:[[8747,8747,8747,8747],256],10868:[[58,58,61],256],10869:[[61,61],256],10870:[[61,61,61],256],10972:[[10973,824],512]},
11264:{11388:[[106],256],11389:[[86],256],11503:[,230],11504:[,230],11505:[,230]},
11520:{11631:[[11617],256],11647:[,9],11744:[,230],11745:[,230],11746:[,230],11747:[,230],11748:[,230],11749:[,230],11750:[,230],11751:[,230],11752:[,230],11753:[,230],11754:[,230],11755:[,230],11756:[,230],11757:[,230],11758:[,230],11759:[,230],11760:[,230],11761:[,230],11762:[,230],11763:[,230],11764:[,230],11765:[,230],11766:[,230],11767:[,230],11768:[,230],11769:[,230],11770:[,230],11771:[,230],11772:[,230],11773:[,230],11774:[,230],11775:[,230]},
11776:{11935:[[27597],256],12019:[[40863],256]},
12032:{12032:[[19968],256],12033:[[20008],256],12034:[[20022],256],12035:[[20031],256],12036:[[20057],256],12037:[[20101],256],12038:[[20108],256],12039:[[20128],256],12040:[[20154],256],12041:[[20799],256],12042:[[20837],256],12043:[[20843],256],12044:[[20866],256],12045:[[20886],256],12046:[[20907],256],12047:[[20960],256],12048:[[20981],256],12049:[[20992],256],12050:[[21147],256],12051:[[21241],256],12052:[[21269],256],12053:[[21274],256],12054:[[21304],256],12055:[[21313],256],12056:[[21340],256],12057:[[21353],256],12058:[[21378],256],12059:[[21430],256],12060:[[21448],256],12061:[[21475],256],12062:[[22231],256],12063:[[22303],256],12064:[[22763],256],12065:[[22786],256],12066:[[22794],256],12067:[[22805],256],12068:[[22823],256],12069:[[22899],256],12070:[[23376],256],12071:[[23424],256],12072:[[23544],256],12073:[[23567],256],12074:[[23586],256],12075:[[23608],256],12076:[[23662],256],12077:[[23665],256],12078:[[24027],256],12079:[[24037],256],12080:[[24049],256],12081:[[24062],256],12082:[[24178],256],12083:[[24186],256],12084:[[24191],256],12085:[[24308],256],12086:[[24318],256],12087:[[24331],256],12088:[[24339],256],12089:[[24400],256],12090:[[24417],256],12091:[[24435],256],12092:[[24515],256],12093:[[25096],256],12094:[[25142],256],12095:[[25163],256],12096:[[25903],256],12097:[[25908],256],12098:[[25991],256],12099:[[26007],256],12100:[[26020],256],12101:[[26041],256],12102:[[26080],256],12103:[[26085],256],12104:[[26352],256],12105:[[26376],256],12106:[[26408],256],12107:[[27424],256],12108:[[27490],256],12109:[[27513],256],12110:[[27571],256],12111:[[27595],256],12112:[[27604],256],12113:[[27611],256],12114:[[27663],256],12115:[[27668],256],12116:[[27700],256],12117:[[28779],256],12118:[[29226],256],12119:[[29238],256],12120:[[29243],256],12121:[[29247],256],12122:[[29255],256],12123:[[29273],256],12124:[[29275],256],12125:[[29356],256],12126:[[29572],256],12127:[[29577],256],12128:[[29916],256],12129:[[29926],256],12130:[[29976],256],12131:[[29983],256],12132:[[29992],256],12133:[[30000],256],12134:[[30091],256],12135:[[30098],256],12136:[[30326],256],12137:[[30333],256],12138:[[30382],256],12139:[[30399],256],12140:[[30446],256],12141:[[30683],256],12142:[[30690],256],12143:[[30707],256],12144:[[31034],256],12145:[[31160],256],12146:[[31166],256],12147:[[31348],256],12148:[[31435],256],12149:[[31481],256],12150:[[31859],256],12151:[[31992],256],12152:[[32566],256],12153:[[32593],256],12154:[[32650],256],12155:[[32701],256],12156:[[32769],256],12157:[[32780],256],12158:[[32786],256],12159:[[32819],256],12160:[[32895],256],12161:[[32905],256],12162:[[33251],256],12163:[[33258],256],12164:[[33267],256],12165:[[33276],256],12166:[[33292],256],12167:[[33307],256],12168:[[33311],256],12169:[[33390],256],12170:[[33394],256],12171:[[33400],256],12172:[[34381],256],12173:[[34411],256],12174:[[34880],256],12175:[[34892],256],12176:[[34915],256],12177:[[35198],256],12178:[[35211],256],12179:[[35282],256],12180:[[35328],256],12181:[[35895],256],12182:[[35910],256],12183:[[35925],256],12184:[[35960],256],12185:[[35997],256],12186:[[36196],256],12187:[[36208],256],12188:[[36275],256],12189:[[36523],256],12190:[[36554],256],12191:[[36763],256],12192:[[36784],256],12193:[[36789],256],12194:[[37009],256],12195:[[37193],256],12196:[[37318],256],12197:[[37324],256],12198:[[37329],256],12199:[[38263],256],12200:[[38272],256],12201:[[38428],256],12202:[[38582],256],12203:[[38585],256],12204:[[38632],256],12205:[[38737],256],12206:[[38750],256],12207:[[38754],256],12208:[[38761],256],12209:[[38859],256],12210:[[38893],256],12211:[[38899],256],12212:[[38913],256],12213:[[39080],256],12214:[[39131],256],12215:[[39135],256],12216:[[39318],256],12217:[[39321],256],12218:[[39340],256],12219:[[39592],256],12220:[[39640],256],12221:[[39647],256],12222:[[39717],256],12223:[[39727],256],12224:[[39730],256],12225:[[39740],256],12226:[[39770],256],12227:[[40165],256],12228:[[40565],256],12229:[[40575],256],12230:[[40613],256],12231:[[40635],256],12232:[[40643],256],12233:[[40653],256],12234:[[40657],256],12235:[[40697],256],12236:[[40701],256],12237:[[40718],256],12238:[[40723],256],12239:[[40736],256],12240:[[40763],256],12241:[[40778],256],12242:[[40786],256],12243:[[40845],256],12244:[[40860],256],12245:[[40864],256]},
12288:{12288:[[32],256],12330:[,218],12331:[,228],12332:[,232],12333:[,222],12334:[,224],12335:[,224],12342:[[12306],256],12344:[[21313],256],12345:[[21316],256],12346:[[21317],256],12358:[,,{12441:12436}],12363:[,,{12441:12364}],12364:[[12363,12441]],12365:[,,{12441:12366}],12366:[[12365,12441]],12367:[,,{12441:12368}],12368:[[12367,12441]],12369:[,,{12441:12370}],12370:[[12369,12441]],12371:[,,{12441:12372}],12372:[[12371,12441]],12373:[,,{12441:12374}],12374:[[12373,12441]],12375:[,,{12441:12376}],12376:[[12375,12441]],12377:[,,{12441:12378}],12378:[[12377,12441]],12379:[,,{12441:12380}],12380:[[12379,12441]],12381:[,,{12441:12382}],12382:[[12381,12441]],12383:[,,{12441:12384}],12384:[[12383,12441]],12385:[,,{12441:12386}],12386:[[12385,12441]],12388:[,,{12441:12389}],12389:[[12388,12441]],12390:[,,{12441:12391}],12391:[[12390,12441]],12392:[,,{12441:12393}],12393:[[12392,12441]],12399:[,,{12441:12400,12442:12401}],12400:[[12399,12441]],12401:[[12399,12442]],12402:[,,{12441:12403,12442:12404}],12403:[[12402,12441]],12404:[[12402,12442]],12405:[,,{12441:12406,12442:12407}],12406:[[12405,12441]],12407:[[12405,12442]],12408:[,,{12441:12409,12442:12410}],12409:[[12408,12441]],12410:[[12408,12442]],12411:[,,{12441:12412,12442:12413}],12412:[[12411,12441]],12413:[[12411,12442]],12436:[[12358,12441]],12441:[,8],12442:[,8],12443:[[32,12441],256],12444:[[32,12442],256],12445:[,,{12441:12446}],12446:[[12445,12441]],12447:[[12424,12426],256],12454:[,,{12441:12532}],12459:[,,{12441:12460}],12460:[[12459,12441]],12461:[,,{12441:12462}],12462:[[12461,12441]],12463:[,,{12441:12464}],12464:[[12463,12441]],12465:[,,{12441:12466}],12466:[[12465,12441]],12467:[,,{12441:12468}],12468:[[12467,12441]],12469:[,,{12441:12470}],12470:[[12469,12441]],12471:[,,{12441:12472}],12472:[[12471,12441]],12473:[,,{12441:12474}],12474:[[12473,12441]],12475:[,,{12441:12476}],12476:[[12475,12441]],12477:[,,{12441:12478}],12478:[[12477,12441]],12479:[,,{12441:12480}],12480:[[12479,12441]],12481:[,,{12441:12482}],12482:[[12481,12441]],12484:[,,{12441:12485}],12485:[[12484,12441]],12486:[,,{12441:12487}],12487:[[12486,12441]],12488:[,,{12441:12489}],12489:[[12488,12441]],12495:[,,{12441:12496,12442:12497}],12496:[[12495,12441]],12497:[[12495,12442]],12498:[,,{12441:12499,12442:12500}],12499:[[12498,12441]],12500:[[12498,12442]],12501:[,,{12441:12502,12442:12503}],12502:[[12501,12441]],12503:[[12501,12442]],12504:[,,{12441:12505,12442:12506}],12505:[[12504,12441]],12506:[[12504,12442]],12507:[,,{12441:12508,12442:12509}],12508:[[12507,12441]],12509:[[12507,12442]],12527:[,,{12441:12535}],12528:[,,{12441:12536}],12529:[,,{12441:12537}],12530:[,,{12441:12538}],12532:[[12454,12441]],12535:[[12527,12441]],12536:[[12528,12441]],12537:[[12529,12441]],12538:[[12530,12441]],12541:[,,{12441:12542}],12542:[[12541,12441]],12543:[[12467,12488],256]},
12544:{12593:[[4352],256],12594:[[4353],256],12595:[[4522],256],12596:[[4354],256],12597:[[4524],256],12598:[[4525],256],12599:[[4355],256],12600:[[4356],256],12601:[[4357],256],12602:[[4528],256],12603:[[4529],256],12604:[[4530],256],12605:[[4531],256],12606:[[4532],256],12607:[[4533],256],12608:[[4378],256],12609:[[4358],256],12610:[[4359],256],12611:[[4360],256],12612:[[4385],256],12613:[[4361],256],12614:[[4362],256],12615:[[4363],256],12616:[[4364],256],12617:[[4365],256],12618:[[4366],256],12619:[[4367],256],12620:[[4368],256],12621:[[4369],256],12622:[[4370],256],12623:[[4449],256],12624:[[4450],256],12625:[[4451],256],12626:[[4452],256],12627:[[4453],256],12628:[[4454],256],12629:[[4455],256],12630:[[4456],256],12631:[[4457],256],12632:[[4458],256],12633:[[4459],256],12634:[[4460],256],12635:[[4461],256],12636:[[4462],256],12637:[[4463],256],12638:[[4464],256],12639:[[4465],256],12640:[[4466],256],12641:[[4467],256],12642:[[4468],256],12643:[[4469],256],12644:[[4448],256],12645:[[4372],256],12646:[[4373],256],12647:[[4551],256],12648:[[4552],256],12649:[[4556],256],12650:[[4558],256],12651:[[4563],256],12652:[[4567],256],12653:[[4569],256],12654:[[4380],256],12655:[[4573],256],12656:[[4575],256],12657:[[4381],256],12658:[[4382],256],12659:[[4384],256],12660:[[4386],256],12661:[[4387],256],12662:[[4391],256],12663:[[4393],256],12664:[[4395],256],12665:[[4396],256],12666:[[4397],256],12667:[[4398],256],12668:[[4399],256],12669:[[4402],256],12670:[[4406],256],12671:[[4416],256],12672:[[4423],256],12673:[[4428],256],12674:[[4593],256],12675:[[4594],256],12676:[[4439],256],12677:[[4440],256],12678:[[4441],256],12679:[[4484],256],12680:[[4485],256],12681:[[4488],256],12682:[[4497],256],12683:[[4498],256],12684:[[4500],256],12685:[[4510],256],12686:[[4513],256],12690:[[19968],256],12691:[[20108],256],12692:[[19977],256],12693:[[22235],256],12694:[[19978],256],12695:[[20013],256],12696:[[19979],256],12697:[[30002],256],12698:[[20057],256],12699:[[19993],256],12700:[[19969],256],12701:[[22825],256],12702:[[22320],256],12703:[[20154],256]},
12800:{12800:[[40,4352,41],256],12801:[[40,4354,41],256],12802:[[40,4355,41],256],12803:[[40,4357,41],256],12804:[[40,4358,41],256],12805:[[40,4359,41],256],12806:[[40,4361,41],256],12807:[[40,4363,41],256],12808:[[40,4364,41],256],12809:[[40,4366,41],256],12810:[[40,4367,41],256],12811:[[40,4368,41],256],12812:[[40,4369,41],256],12813:[[40,4370,41],256],12814:[[40,4352,4449,41],256],12815:[[40,4354,4449,41],256],12816:[[40,4355,4449,41],256],12817:[[40,4357,4449,41],256],12818:[[40,4358,4449,41],256],12819:[[40,4359,4449,41],256],12820:[[40,4361,4449,41],256],12821:[[40,4363,4449,41],256],12822:[[40,4364,4449,41],256],12823:[[40,4366,4449,41],256],12824:[[40,4367,4449,41],256],12825:[[40,4368,4449,41],256],12826:[[40,4369,4449,41],256],12827:[[40,4370,4449,41],256],12828:[[40,4364,4462,41],256],12829:[[40,4363,4457,4364,4453,4523,41],256],12830:[[40,4363,4457,4370,4462,41],256],12832:[[40,19968,41],256],12833:[[40,20108,41],256],12834:[[40,19977,41],256],12835:[[40,22235,41],256],12836:[[40,20116,41],256],12837:[[40,20845,41],256],12838:[[40,19971,41],256],12839:[[40,20843,41],256],12840:[[40,20061,41],256],12841:[[40,21313,41],256],12842:[[40,26376,41],256],12843:[[40,28779,41],256],12844:[[40,27700,41],256],12845:[[40,26408,41],256],12846:[[40,37329,41],256],12847:[[40,22303,41],256],12848:[[40,26085,41],256],12849:[[40,26666,41],256],12850:[[40,26377,41],256],12851:[[40,31038,41],256],12852:[[40,21517,41],256],12853:[[40,29305,41],256],12854:[[40,36001,41],256],12855:[[40,31069,41],256],12856:[[40,21172,41],256],12857:[[40,20195,41],256],12858:[[40,21628,41],256],12859:[[40,23398,41],256],12860:[[40,30435,41],256],12861:[[40,20225,41],256],12862:[[40,36039,41],256],12863:[[40,21332,41],256],12864:[[40,31085,41],256],12865:[[40,20241,41],256],12866:[[40,33258,41],256],12867:[[40,33267,41],256],12868:[[21839],256],12869:[[24188],256],12870:[[25991],256],12871:[[31631],256],12880:[[80,84,69],256],12881:[[50,49],256],12882:[[50,50],256],12883:[[50,51],256],12884:[[50,52],256],12885:[[50,53],256],12886:[[50,54],256],12887:[[50,55],256],12888:[[50,56],256],12889:[[50,57],256],12890:[[51,48],256],12891:[[51,49],256],12892:[[51,50],256],12893:[[51,51],256],12894:[[51,52],256],12895:[[51,53],256],12896:[[4352],256],12897:[[4354],256],12898:[[4355],256],12899:[[4357],256],12900:[[4358],256],12901:[[4359],256],12902:[[4361],256],12903:[[4363],256],12904:[[4364],256],12905:[[4366],256],12906:[[4367],256],12907:[[4368],256],12908:[[4369],256],12909:[[4370],256],12910:[[4352,4449],256],12911:[[4354,4449],256],12912:[[4355,4449],256],12913:[[4357,4449],256],12914:[[4358,4449],256],12915:[[4359,4449],256],12916:[[4361,4449],256],12917:[[4363,4449],256],12918:[[4364,4449],256],12919:[[4366,4449],256],12920:[[4367,4449],256],12921:[[4368,4449],256],12922:[[4369,4449],256],12923:[[4370,4449],256],12924:[[4366,4449,4535,4352,4457],256],12925:[[4364,4462,4363,4468],256],12926:[[4363,4462],256],12928:[[19968],256],12929:[[20108],256],12930:[[19977],256],12931:[[22235],256],12932:[[20116],256],12933:[[20845],256],12934:[[19971],256],12935:[[20843],256],12936:[[20061],256],12937:[[21313],256],12938:[[26376],256],12939:[[28779],256],12940:[[27700],256],12941:[[26408],256],12942:[[37329],256],12943:[[22303],256],12944:[[26085],256],12945:[[26666],256],12946:[[26377],256],12947:[[31038],256],12948:[[21517],256],12949:[[29305],256],12950:[[36001],256],12951:[[31069],256],12952:[[21172],256],12953:[[31192],256],12954:[[30007],256],12955:[[22899],256],12956:[[36969],256],12957:[[20778],256],12958:[[21360],256],12959:[[27880],256],12960:[[38917],256],12961:[[20241],256],12962:[[20889],256],12963:[[27491],256],12964:[[19978],256],12965:[[20013],256],12966:[[19979],256],12967:[[24038],256],12968:[[21491],256],12969:[[21307],256],12970:[[23447],256],12971:[[23398],256],12972:[[30435],256],12973:[[20225],256],12974:[[36039],256],12975:[[21332],256],12976:[[22812],256],12977:[[51,54],256],12978:[[51,55],256],12979:[[51,56],256],12980:[[51,57],256],12981:[[52,48],256],12982:[[52,49],256],12983:[[52,50],256],12984:[[52,51],256],12985:[[52,52],256],12986:[[52,53],256],12987:[[52,54],256],12988:[[52,55],256],12989:[[52,56],256],12990:[[52,57],256],12991:[[53,48],256],12992:[[49,26376],256],12993:[[50,26376],256],12994:[[51,26376],256],12995:[[52,26376],256],12996:[[53,26376],256],12997:[[54,26376],256],12998:[[55,26376],256],12999:[[56,26376],256],13000:[[57,26376],256],13001:[[49,48,26376],256],13002:[[49,49,26376],256],13003:[[49,50,26376],256],13004:[[72,103],256],13005:[[101,114,103],256],13006:[[101,86],256],13007:[[76,84,68],256],13008:[[12450],256],13009:[[12452],256],13010:[[12454],256],13011:[[12456],256],13012:[[12458],256],13013:[[12459],256],13014:[[12461],256],13015:[[12463],256],13016:[[12465],256],13017:[[12467],256],13018:[[12469],256],13019:[[12471],256],13020:[[12473],256],13021:[[12475],256],13022:[[12477],256],13023:[[12479],256],13024:[[12481],256],13025:[[12484],256],13026:[[12486],256],13027:[[12488],256],13028:[[12490],256],13029:[[12491],256],13030:[[12492],256],13031:[[12493],256],13032:[[12494],256],13033:[[12495],256],13034:[[12498],256],13035:[[12501],256],13036:[[12504],256],13037:[[12507],256],13038:[[12510],256],13039:[[12511],256],13040:[[12512],256],13041:[[12513],256],13042:[[12514],256],13043:[[12516],256],13044:[[12518],256],13045:[[12520],256],13046:[[12521],256],13047:[[12522],256],13048:[[12523],256],13049:[[12524],256],13050:[[12525],256],13051:[[12527],256],13052:[[12528],256],13053:[[12529],256],13054:[[12530],256]},
13056:{13056:[[12450,12497,12540,12488],256],13057:[[12450,12523,12501,12449],256],13058:[[12450,12531,12506,12450],256],13059:[[12450,12540,12523],256],13060:[[12452,12491,12531,12464],256],13061:[[12452,12531,12481],256],13062:[[12454,12457,12531],256],13063:[[12456,12473,12463,12540,12489],256],13064:[[12456,12540,12459,12540],256],13065:[[12458,12531,12473],256],13066:[[12458,12540,12512],256],13067:[[12459,12452,12522],256],13068:[[12459,12521,12483,12488],256],13069:[[12459,12525,12522,12540],256],13070:[[12460,12525,12531],256],13071:[[12460,12531,12510],256],13072:[[12462,12460],256],13073:[[12462,12491,12540],256],13074:[[12461,12517,12522,12540],256],13075:[[12462,12523,12480,12540],256],13076:[[12461,12525],256],13077:[[12461,12525,12464,12521,12512],256],13078:[[12461,12525,12513,12540,12488,12523],256],13079:[[12461,12525,12527,12483,12488],256],13080:[[12464,12521,12512],256],13081:[[12464,12521,12512,12488,12531],256],13082:[[12463,12523,12476,12452,12525],256],13083:[[12463,12525,12540,12493],256],13084:[[12465,12540,12473],256],13085:[[12467,12523,12490],256],13086:[[12467,12540,12509],256],13087:[[12469,12452,12463,12523],256],13088:[[12469,12531,12481,12540,12512],256],13089:[[12471,12522,12531,12464],256],13090:[[12475,12531,12481],256],13091:[[12475,12531,12488],256],13092:[[12480,12540,12473],256],13093:[[12487,12471],256],13094:[[12489,12523],256],13095:[[12488,12531],256],13096:[[12490,12494],256],13097:[[12494,12483,12488],256],13098:[[12495,12452,12484],256],13099:[[12497,12540,12475,12531,12488],256],13100:[[12497,12540,12484],256],13101:[[12496,12540,12524,12523],256],13102:[[12500,12450,12473,12488,12523],256],13103:[[12500,12463,12523],256],13104:[[12500,12467],256],13105:[[12499,12523],256],13106:[[12501,12449,12521,12483,12489],256],13107:[[12501,12451,12540,12488],256],13108:[[12502,12483,12471,12455,12523],256],13109:[[12501,12521,12531],256],13110:[[12504,12463,12479,12540,12523],256],13111:[[12506,12477],256],13112:[[12506,12491,12498],256],13113:[[12504,12523,12484],256],13114:[[12506,12531,12473],256],13115:[[12506,12540,12472],256],13116:[[12505,12540,12479],256],13117:[[12509,12452,12531,12488],256],13118:[[12508,12523,12488],256],13119:[[12507,12531],256],13120:[[12509,12531,12489],256],13121:[[12507,12540,12523],256],13122:[[12507,12540,12531],256],13123:[[12510,12452,12463,12525],256],13124:[[12510,12452,12523],256],13125:[[12510,12483,12495],256],13126:[[12510,12523,12463],256],13127:[[12510,12531,12471,12519,12531],256],13128:[[12511,12463,12525,12531],256],13129:[[12511,12522],256],13130:[[12511,12522,12496,12540,12523],256],13131:[[12513,12460],256],13132:[[12513,12460,12488,12531],256],13133:[[12513,12540,12488,12523],256],13134:[[12516,12540,12489],256],13135:[[12516,12540,12523],256],13136:[[12518,12450,12531],256],13137:[[12522,12483,12488,12523],256],13138:[[12522,12521],256],13139:[[12523,12500,12540],256],13140:[[12523,12540,12502,12523],256],13141:[[12524,12512],256],13142:[[12524,12531,12488,12466,12531],256],13143:[[12527,12483,12488],256],13144:[[48,28857],256],13145:[[49,28857],256],13146:[[50,28857],256],13147:[[51,28857],256],13148:[[52,28857],256],13149:[[53,28857],256],13150:[[54,28857],256],13151:[[55,28857],256],13152:[[56,28857],256],13153:[[57,28857],256],13154:[[49,48,28857],256],13155:[[49,49,28857],256],13156:[[49,50,28857],256],13157:[[49,51,28857],256],13158:[[49,52,28857],256],13159:[[49,53,28857],256],13160:[[49,54,28857],256],13161:[[49,55,28857],256],13162:[[49,56,28857],256],13163:[[49,57,28857],256],13164:[[50,48,28857],256],13165:[[50,49,28857],256],13166:[[50,50,28857],256],13167:[[50,51,28857],256],13168:[[50,52,28857],256],13169:[[104,80,97],256],13170:[[100,97],256],13171:[[65,85],256],13172:[[98,97,114],256],13173:[[111,86],256],13174:[[112,99],256],13175:[[100,109],256],13176:[[100,109,178],256],13177:[[100,109,179],256],13178:[[73,85],256],13179:[[24179,25104],256],13180:[[26157,21644],256],13181:[[22823,27491],256],13182:[[26126,27835],256],13183:[[26666,24335,20250,31038],256],13184:[[112,65],256],13185:[[110,65],256],13186:[[956,65],256],13187:[[109,65],256],13188:[[107,65],256],13189:[[75,66],256],13190:[[77,66],256],13191:[[71,66],256],13192:[[99,97,108],256],13193:[[107,99,97,108],256],13194:[[112,70],256],13195:[[110,70],256],13196:[[956,70],256],13197:[[956,103],256],13198:[[109,103],256],13199:[[107,103],256],13200:[[72,122],256],13201:[[107,72,122],256],13202:[[77,72,122],256],13203:[[71,72,122],256],13204:[[84,72,122],256],13205:[[956,8467],256],13206:[[109,8467],256],13207:[[100,8467],256],13208:[[107,8467],256],13209:[[102,109],256],13210:[[110,109],256],13211:[[956,109],256],13212:[[109,109],256],13213:[[99,109],256],13214:[[107,109],256],13215:[[109,109,178],256],13216:[[99,109,178],256],13217:[[109,178],256],13218:[[107,109,178],256],13219:[[109,109,179],256],13220:[[99,109,179],256],13221:[[109,179],256],13222:[[107,109,179],256],13223:[[109,8725,115],256],13224:[[109,8725,115,178],256],13225:[[80,97],256],13226:[[107,80,97],256],13227:[[77,80,97],256],13228:[[71,80,97],256],13229:[[114,97,100],256],13230:[[114,97,100,8725,115],256],13231:[[114,97,100,8725,115,178],256],13232:[[112,115],256],13233:[[110,115],256],13234:[[956,115],256],13235:[[109,115],256],13236:[[112,86],256],13237:[[110,86],256],13238:[[956,86],256],13239:[[109,86],256],13240:[[107,86],256],13241:[[77,86],256],13242:[[112,87],256],13243:[[110,87],256],13244:[[956,87],256],13245:[[109,87],256],13246:[[107,87],256],13247:[[77,87],256],13248:[[107,937],256],13249:[[77,937],256],13250:[[97,46,109,46],256],13251:[[66,113],256],13252:[[99,99],256],13253:[[99,100],256],13254:[[67,8725,107,103],256],13255:[[67,111,46],256],13256:[[100,66],256],13257:[[71,121],256],13258:[[104,97],256],13259:[[72,80],256],13260:[[105,110],256],13261:[[75,75],256],13262:[[75,77],256],13263:[[107,116],256],13264:[[108,109],256],13265:[[108,110],256],13266:[[108,111,103],256],13267:[[108,120],256],13268:[[109,98],256],13269:[[109,105,108],256],13270:[[109,111,108],256],13271:[[80,72],256],13272:[[112,46,109,46],256],13273:[[80,80,77],256],13274:[[80,82],256],13275:[[115,114],256],13276:[[83,118],256],13277:[[87,98],256],13278:[[86,8725,109],256],13279:[[65,8725,109],256],13280:[[49,26085],256],13281:[[50,26085],256],13282:[[51,26085],256],13283:[[52,26085],256],13284:[[53,26085],256],13285:[[54,26085],256],13286:[[55,26085],256],13287:[[56,26085],256],13288:[[57,26085],256],13289:[[49,48,26085],256],13290:[[49,49,26085],256],13291:[[49,50,26085],256],13292:[[49,51,26085],256],13293:[[49,52,26085],256],13294:[[49,53,26085],256],13295:[[49,54,26085],256],13296:[[49,55,26085],256],13297:[[49,56,26085],256],13298:[[49,57,26085],256],13299:[[50,48,26085],256],13300:[[50,49,26085],256],13301:[[50,50,26085],256],13302:[[50,51,26085],256],13303:[[50,52,26085],256],13304:[[50,53,26085],256],13305:[[50,54,26085],256],13306:[[50,55,26085],256],13307:[[50,56,26085],256],13308:[[50,57,26085],256],13309:[[51,48,26085],256],13310:[[51,49,26085],256],13311:[[103,97,108],256]},
27136:{92912:[,1],92913:[,1],92914:[,1],92915:[,1],92916:[,1]},
27392:{92976:[,230],92977:[,230],92978:[,230],92979:[,230],92980:[,230],92981:[,230],92982:[,230]},
42496:{42607:[,230],42612:[,230],42613:[,230],42614:[,230],42615:[,230],42616:[,230],42617:[,230],42618:[,230],42619:[,230],42620:[,230],42621:[,230],42652:[[1098],256],42653:[[1100],256],42655:[,230],42736:[,230],42737:[,230]},
42752:{42864:[[42863],256],43000:[[294],256],43001:[[339],256]},
43008:{43014:[,9],43204:[,9],43232:[,230],43233:[,230],43234:[,230],43235:[,230],43236:[,230],43237:[,230],43238:[,230],43239:[,230],43240:[,230],43241:[,230],43242:[,230],43243:[,230],43244:[,230],43245:[,230],43246:[,230],43247:[,230],43248:[,230],43249:[,230]},
43264:{43307:[,220],43308:[,220],43309:[,220],43347:[,9],43443:[,7],43456:[,9]},
43520:{43696:[,230],43698:[,230],43699:[,230],43700:[,220],43703:[,230],43704:[,230],43710:[,230],43711:[,230],43713:[,230],43766:[,9]},
43776:{43868:[[42791],256],43869:[[43831],256],43870:[[619],256],43871:[[43858],256],44013:[,9]},
48128:{113822:[,1]},
53504:{119134:[[119127,119141],512],119135:[[119128,119141],512],119136:[[119135,119150],512],119137:[[119135,119151],512],119138:[[119135,119152],512],119139:[[119135,119153],512],119140:[[119135,119154],512],119141:[,216],119142:[,216],119143:[,1],119144:[,1],119145:[,1],119149:[,226],119150:[,216],119151:[,216],119152:[,216],119153:[,216],119154:[,216],119163:[,220],119164:[,220],119165:[,220],119166:[,220],119167:[,220],119168:[,220],119169:[,220],119170:[,220],119173:[,230],119174:[,230],119175:[,230],119176:[,230],119177:[,230],119178:[,220],119179:[,220],119210:[,230],119211:[,230],119212:[,230],119213:[,230],119227:[[119225,119141],512],119228:[[119226,119141],512],119229:[[119227,119150],512],119230:[[119228,119150],512],119231:[[119227,119151],512],119232:[[119228,119151],512]},
53760:{119362:[,230],119363:[,230],119364:[,230]},
54272:{119808:[[65],256],119809:[[66],256],119810:[[67],256],119811:[[68],256],119812:[[69],256],119813:[[70],256],119814:[[71],256],119815:[[72],256],119816:[[73],256],119817:[[74],256],119818:[[75],256],119819:[[76],256],119820:[[77],256],119821:[[78],256],119822:[[79],256],119823:[[80],256],119824:[[81],256],119825:[[82],256],119826:[[83],256],119827:[[84],256],119828:[[85],256],119829:[[86],256],119830:[[87],256],119831:[[88],256],119832:[[89],256],119833:[[90],256],119834:[[97],256],119835:[[98],256],119836:[[99],256],119837:[[100],256],119838:[[101],256],119839:[[102],256],119840:[[103],256],119841:[[104],256],119842:[[105],256],119843:[[106],256],119844:[[107],256],119845:[[108],256],119846:[[109],256],119847:[[110],256],119848:[[111],256],119849:[[112],256],119850:[[113],256],119851:[[114],256],119852:[[115],256],119853:[[116],256],119854:[[117],256],119855:[[118],256],119856:[[119],256],119857:[[120],256],119858:[[121],256],119859:[[122],256],119860:[[65],256],119861:[[66],256],119862:[[67],256],119863:[[68],256],119864:[[69],256],119865:[[70],256],119866:[[71],256],119867:[[72],256],119868:[[73],256],119869:[[74],256],119870:[[75],256],119871:[[76],256],119872:[[77],256],119873:[[78],256],119874:[[79],256],119875:[[80],256],119876:[[81],256],119877:[[82],256],119878:[[83],256],119879:[[84],256],119880:[[85],256],119881:[[86],256],119882:[[87],256],119883:[[88],256],119884:[[89],256],119885:[[90],256],119886:[[97],256],119887:[[98],256],119888:[[99],256],119889:[[100],256],119890:[[101],256],119891:[[102],256],119892:[[103],256],119894:[[105],256],119895:[[106],256],119896:[[107],256],119897:[[108],256],119898:[[109],256],119899:[[110],256],119900:[[111],256],119901:[[112],256],119902:[[113],256],119903:[[114],256],119904:[[115],256],119905:[[116],256],119906:[[117],256],119907:[[118],256],119908:[[119],256],119909:[[120],256],119910:[[121],256],119911:[[122],256],119912:[[65],256],119913:[[66],256],119914:[[67],256],119915:[[68],256],119916:[[69],256],119917:[[70],256],119918:[[71],256],119919:[[72],256],119920:[[73],256],119921:[[74],256],119922:[[75],256],119923:[[76],256],119924:[[77],256],119925:[[78],256],119926:[[79],256],119927:[[80],256],119928:[[81],256],119929:[[82],256],119930:[[83],256],119931:[[84],256],119932:[[85],256],119933:[[86],256],119934:[[87],256],119935:[[88],256],119936:[[89],256],119937:[[90],256],119938:[[97],256],119939:[[98],256],119940:[[99],256],119941:[[100],256],119942:[[101],256],119943:[[102],256],119944:[[103],256],119945:[[104],256],119946:[[105],256],119947:[[106],256],119948:[[107],256],119949:[[108],256],119950:[[109],256],119951:[[110],256],119952:[[111],256],119953:[[112],256],119954:[[113],256],119955:[[114],256],119956:[[115],256],119957:[[116],256],119958:[[117],256],119959:[[118],256],119960:[[119],256],119961:[[120],256],119962:[[121],256],119963:[[122],256],119964:[[65],256],119966:[[67],256],119967:[[68],256],119970:[[71],256],119973:[[74],256],119974:[[75],256],119977:[[78],256],119978:[[79],256],119979:[[80],256],119980:[[81],256],119982:[[83],256],119983:[[84],256],119984:[[85],256],119985:[[86],256],119986:[[87],256],119987:[[88],256],119988:[[89],256],119989:[[90],256],119990:[[97],256],119991:[[98],256],119992:[[99],256],119993:[[100],256],119995:[[102],256],119997:[[104],256],119998:[[105],256],119999:[[106],256],120000:[[107],256],120001:[[108],256],120002:[[109],256],120003:[[110],256],120005:[[112],256],120006:[[113],256],120007:[[114],256],120008:[[115],256],120009:[[116],256],120010:[[117],256],120011:[[118],256],120012:[[119],256],120013:[[120],256],120014:[[121],256],120015:[[122],256],120016:[[65],256],120017:[[66],256],120018:[[67],256],120019:[[68],256],120020:[[69],256],120021:[[70],256],120022:[[71],256],120023:[[72],256],120024:[[73],256],120025:[[74],256],120026:[[75],256],120027:[[76],256],120028:[[77],256],120029:[[78],256],120030:[[79],256],120031:[[80],256],120032:[[81],256],120033:[[82],256],120034:[[83],256],120035:[[84],256],120036:[[85],256],120037:[[86],256],120038:[[87],256],120039:[[88],256],120040:[[89],256],120041:[[90],256],120042:[[97],256],120043:[[98],256],120044:[[99],256],120045:[[100],256],120046:[[101],256],120047:[[102],256],120048:[[103],256],120049:[[104],256],120050:[[105],256],120051:[[106],256],120052:[[107],256],120053:[[108],256],120054:[[109],256],120055:[[110],256],120056:[[111],256],120057:[[112],256],120058:[[113],256],120059:[[114],256],120060:[[115],256],120061:[[116],256],120062:[[117],256],120063:[[118],256]},
54528:{120064:[[119],256],120065:[[120],256],120066:[[121],256],120067:[[122],256],120068:[[65],256],120069:[[66],256],120071:[[68],256],120072:[[69],256],120073:[[70],256],120074:[[71],256],120077:[[74],256],120078:[[75],256],120079:[[76],256],120080:[[77],256],120081:[[78],256],120082:[[79],256],120083:[[80],256],120084:[[81],256],120086:[[83],256],120087:[[84],256],120088:[[85],256],120089:[[86],256],120090:[[87],256],120091:[[88],256],120092:[[89],256],120094:[[97],256],120095:[[98],256],120096:[[99],256],120097:[[100],256],120098:[[101],256],120099:[[102],256],120100:[[103],256],120101:[[104],256],120102:[[105],256],120103:[[106],256],120104:[[107],256],120105:[[108],256],120106:[[109],256],120107:[[110],256],120108:[[111],256],120109:[[112],256],120110:[[113],256],120111:[[114],256],120112:[[115],256],120113:[[116],256],120114:[[117],256],120115:[[118],256],120116:[[119],256],120117:[[120],256],120118:[[121],256],120119:[[122],256],120120:[[65],256],120121:[[66],256],120123:[[68],256],120124:[[69],256],120125:[[70],256],120126:[[71],256],120128:[[73],256],120129:[[74],256],120130:[[75],256],120131:[[76],256],120132:[[77],256],120134:[[79],256],120138:[[83],256],120139:[[84],256],120140:[[85],256],120141:[[86],256],120142:[[87],256],120143:[[88],256],120144:[[89],256],120146:[[97],256],120147:[[98],256],120148:[[99],256],120149:[[100],256],120150:[[101],256],120151:[[102],256],120152:[[103],256],120153:[[104],256],120154:[[105],256],120155:[[106],256],120156:[[107],256],120157:[[108],256],120158:[[109],256],120159:[[110],256],120160:[[111],256],120161:[[112],256],120162:[[113],256],120163:[[114],256],120164:[[115],256],120165:[[116],256],120166:[[117],256],120167:[[118],256],120168:[[119],256],120169:[[120],256],120170:[[121],256],120171:[[122],256],120172:[[65],256],120173:[[66],256],120174:[[67],256],120175:[[68],256],120176:[[69],256],120177:[[70],256],120178:[[71],256],120179:[[72],256],120180:[[73],256],120181:[[74],256],120182:[[75],256],120183:[[76],256],120184:[[77],256],120185:[[78],256],120186:[[79],256],120187:[[80],256],120188:[[81],256],120189:[[82],256],120190:[[83],256],120191:[[84],256],120192:[[85],256],120193:[[86],256],120194:[[87],256],120195:[[88],256],120196:[[89],256],120197:[[90],256],120198:[[97],256],120199:[[98],256],120200:[[99],256],120201:[[100],256],120202:[[101],256],120203:[[102],256],120204:[[103],256],120205:[[104],256],120206:[[105],256],120207:[[106],256],120208:[[107],256],120209:[[108],256],120210:[[109],256],120211:[[110],256],120212:[[111],256],120213:[[112],256],120214:[[113],256],120215:[[114],256],120216:[[115],256],120217:[[116],256],120218:[[117],256],120219:[[118],256],120220:[[119],256],120221:[[120],256],120222:[[121],256],120223:[[122],256],120224:[[65],256],120225:[[66],256],120226:[[67],256],120227:[[68],256],120228:[[69],256],120229:[[70],256],120230:[[71],256],120231:[[72],256],120232:[[73],256],120233:[[74],256],120234:[[75],256],120235:[[76],256],120236:[[77],256],120237:[[78],256],120238:[[79],256],120239:[[80],256],120240:[[81],256],120241:[[82],256],120242:[[83],256],120243:[[84],256],120244:[[85],256],120245:[[86],256],120246:[[87],256],120247:[[88],256],120248:[[89],256],120249:[[90],256],120250:[[97],256],120251:[[98],256],120252:[[99],256],120253:[[100],256],120254:[[101],256],120255:[[102],256],120256:[[103],256],120257:[[104],256],120258:[[105],256],120259:[[106],256],120260:[[107],256],120261:[[108],256],120262:[[109],256],120263:[[110],256],120264:[[111],256],120265:[[112],256],120266:[[113],256],120267:[[114],256],120268:[[115],256],120269:[[116],256],120270:[[117],256],120271:[[118],256],120272:[[119],256],120273:[[120],256],120274:[[121],256],120275:[[122],256],120276:[[65],256],120277:[[66],256],120278:[[67],256],120279:[[68],256],120280:[[69],256],120281:[[70],256],120282:[[71],256],120283:[[72],256],120284:[[73],256],120285:[[74],256],120286:[[75],256],120287:[[76],256],120288:[[77],256],120289:[[78],256],120290:[[79],256],120291:[[80],256],120292:[[81],256],120293:[[82],256],120294:[[83],256],120295:[[84],256],120296:[[85],256],120297:[[86],256],120298:[[87],256],120299:[[88],256],120300:[[89],256],120301:[[90],256],120302:[[97],256],120303:[[98],256],120304:[[99],256],120305:[[100],256],120306:[[101],256],120307:[[102],256],120308:[[103],256],120309:[[104],256],120310:[[105],256],120311:[[106],256],120312:[[107],256],120313:[[108],256],120314:[[109],256],120315:[[110],256],120316:[[111],256],120317:[[112],256],120318:[[113],256],120319:[[114],256]},
54784:{120320:[[115],256],120321:[[116],256],120322:[[117],256],120323:[[118],256],120324:[[119],256],120325:[[120],256],120326:[[121],256],120327:[[122],256],120328:[[65],256],120329:[[66],256],120330:[[67],256],120331:[[68],256],120332:[[69],256],120333:[[70],256],120334:[[71],256],120335:[[72],256],120336:[[73],256],120337:[[74],256],120338:[[75],256],120339:[[76],256],120340:[[77],256],120341:[[78],256],120342:[[79],256],120343:[[80],256],120344:[[81],256],120345:[[82],256],120346:[[83],256],120347:[[84],256],120348:[[85],256],120349:[[86],256],120350:[[87],256],120351:[[88],256],120352:[[89],256],120353:[[90],256],120354:[[97],256],120355:[[98],256],120356:[[99],256],120357:[[100],256],120358:[[101],256],120359:[[102],256],120360:[[103],256],120361:[[104],256],120362:[[105],256],120363:[[106],256],120364:[[107],256],120365:[[108],256],120366:[[109],256],120367:[[110],256],120368:[[111],256],120369:[[112],256],120370:[[113],256],120371:[[114],256],120372:[[115],256],120373:[[116],256],120374:[[117],256],120375:[[118],256],120376:[[119],256],120377:[[120],256],120378:[[121],256],120379:[[122],256],120380:[[65],256],120381:[[66],256],120382:[[67],256],120383:[[68],256],120384:[[69],256],120385:[[70],256],120386:[[71],256],120387:[[72],256],120388:[[73],256],120389:[[74],256],120390:[[75],256],120391:[[76],256],120392:[[77],256],120393:[[78],256],120394:[[79],256],120395:[[80],256],120396:[[81],256],120397:[[82],256],120398:[[83],256],120399:[[84],256],120400:[[85],256],120401:[[86],256],120402:[[87],256],120403:[[88],256],120404:[[89],256],120405:[[90],256],120406:[[97],256],120407:[[98],256],120408:[[99],256],120409:[[100],256],120410:[[101],256],120411:[[102],256],120412:[[103],256],120413:[[104],256],120414:[[105],256],120415:[[106],256],120416:[[107],256],120417:[[108],256],120418:[[109],256],120419:[[110],256],120420:[[111],256],120421:[[112],256],120422:[[113],256],120423:[[114],256],120424:[[115],256],120425:[[116],256],120426:[[117],256],120427:[[118],256],120428:[[119],256],120429:[[120],256],120430:[[121],256],120431:[[122],256],120432:[[65],256],120433:[[66],256],120434:[[67],256],120435:[[68],256],120436:[[69],256],120437:[[70],256],120438:[[71],256],120439:[[72],256],120440:[[73],256],120441:[[74],256],120442:[[75],256],120443:[[76],256],120444:[[77],256],120445:[[78],256],120446:[[79],256],120447:[[80],256],120448:[[81],256],120449:[[82],256],120450:[[83],256],120451:[[84],256],120452:[[85],256],120453:[[86],256],120454:[[87],256],120455:[[88],256],120456:[[89],256],120457:[[90],256],120458:[[97],256],120459:[[98],256],120460:[[99],256],120461:[[100],256],120462:[[101],256],120463:[[102],256],120464:[[103],256],120465:[[104],256],120466:[[105],256],120467:[[106],256],120468:[[107],256],120469:[[108],256],120470:[[109],256],120471:[[110],256],120472:[[111],256],120473:[[112],256],120474:[[113],256],120475:[[114],256],120476:[[115],256],120477:[[116],256],120478:[[117],256],120479:[[118],256],120480:[[119],256],120481:[[120],256],120482:[[121],256],120483:[[122],256],120484:[[305],256],120485:[[567],256],120488:[[913],256],120489:[[914],256],120490:[[915],256],120491:[[916],256],120492:[[917],256],120493:[[918],256],120494:[[919],256],120495:[[920],256],120496:[[921],256],120497:[[922],256],120498:[[923],256],120499:[[924],256],120500:[[925],256],120501:[[926],256],120502:[[927],256],120503:[[928],256],120504:[[929],256],120505:[[1012],256],120506:[[931],256],120507:[[932],256],120508:[[933],256],120509:[[934],256],120510:[[935],256],120511:[[936],256],120512:[[937],256],120513:[[8711],256],120514:[[945],256],120515:[[946],256],120516:[[947],256],120517:[[948],256],120518:[[949],256],120519:[[950],256],120520:[[951],256],120521:[[952],256],120522:[[953],256],120523:[[954],256],120524:[[955],256],120525:[[956],256],120526:[[957],256],120527:[[958],256],120528:[[959],256],120529:[[960],256],120530:[[961],256],120531:[[962],256],120532:[[963],256],120533:[[964],256],120534:[[965],256],120535:[[966],256],120536:[[967],256],120537:[[968],256],120538:[[969],256],120539:[[8706],256],120540:[[1013],256],120541:[[977],256],120542:[[1008],256],120543:[[981],256],120544:[[1009],256],120545:[[982],256],120546:[[913],256],120547:[[914],256],120548:[[915],256],120549:[[916],256],120550:[[917],256],120551:[[918],256],120552:[[919],256],120553:[[920],256],120554:[[921],256],120555:[[922],256],120556:[[923],256],120557:[[924],256],120558:[[925],256],120559:[[926],256],120560:[[927],256],120561:[[928],256],120562:[[929],256],120563:[[1012],256],120564:[[931],256],120565:[[932],256],120566:[[933],256],120567:[[934],256],120568:[[935],256],120569:[[936],256],120570:[[937],256],120571:[[8711],256],120572:[[945],256],120573:[[946],256],120574:[[947],256],120575:[[948],256]},
55040:{120576:[[949],256],120577:[[950],256],120578:[[951],256],120579:[[952],256],120580:[[953],256],120581:[[954],256],120582:[[955],256],120583:[[956],256],120584:[[957],256],120585:[[958],256],120586:[[959],256],120587:[[960],256],120588:[[961],256],120589:[[962],256],120590:[[963],256],120591:[[964],256],120592:[[965],256],120593:[[966],256],120594:[[967],256],120595:[[968],256],120596:[[969],256],120597:[[8706],256],120598:[[1013],256],120599:[[977],256],120600:[[1008],256],120601:[[981],256],120602:[[1009],256],120603:[[982],256],120604:[[913],256],120605:[[914],256],120606:[[915],256],120607:[[916],256],120608:[[917],256],120609:[[918],256],120610:[[919],256],120611:[[920],256],120612:[[921],256],120613:[[922],256],120614:[[923],256],120615:[[924],256],120616:[[925],256],120617:[[926],256],120618:[[927],256],120619:[[928],256],120620:[[929],256],120621:[[1012],256],120622:[[931],256],120623:[[932],256],120624:[[933],256],120625:[[934],256],120626:[[935],256],120627:[[936],256],120628:[[937],256],120629:[[8711],256],120630:[[945],256],120631:[[946],256],120632:[[947],256],120633:[[948],256],120634:[[949],256],120635:[[950],256],120636:[[951],256],120637:[[952],256],120638:[[953],256],120639:[[954],256],120640:[[955],256],120641:[[956],256],120642:[[957],256],120643:[[958],256],120644:[[959],256],120645:[[960],256],120646:[[961],256],120647:[[962],256],120648:[[963],256],120649:[[964],256],120650:[[965],256],120651:[[966],256],120652:[[967],256],120653:[[968],256],120654:[[969],256],120655:[[8706],256],120656:[[1013],256],120657:[[977],256],120658:[[1008],256],120659:[[981],256],120660:[[1009],256],120661:[[982],256],120662:[[913],256],120663:[[914],256],120664:[[915],256],120665:[[916],256],120666:[[917],256],120667:[[918],256],120668:[[919],256],120669:[[920],256],120670:[[921],256],120671:[[922],256],120672:[[923],256],120673:[[924],256],120674:[[925],256],120675:[[926],256],120676:[[927],256],120677:[[928],256],120678:[[929],256],120679:[[1012],256],120680:[[931],256],120681:[[932],256],120682:[[933],256],120683:[[934],256],120684:[[935],256],120685:[[936],256],120686:[[937],256],120687:[[8711],256],120688:[[945],256],120689:[[946],256],120690:[[947],256],120691:[[948],256],120692:[[949],256],120693:[[950],256],120694:[[951],256],120695:[[952],256],120696:[[953],256],120697:[[954],256],120698:[[955],256],120699:[[956],256],120700:[[957],256],120701:[[958],256],120702:[[959],256],120703:[[960],256],120704:[[961],256],120705:[[962],256],120706:[[963],256],120707:[[964],256],120708:[[965],256],120709:[[966],256],120710:[[967],256],120711:[[968],256],120712:[[969],256],120713:[[8706],256],120714:[[1013],256],120715:[[977],256],120716:[[1008],256],120717:[[981],256],120718:[[1009],256],120719:[[982],256],120720:[[913],256],120721:[[914],256],120722:[[915],256],120723:[[916],256],120724:[[917],256],120725:[[918],256],120726:[[919],256],120727:[[920],256],120728:[[921],256],120729:[[922],256],120730:[[923],256],120731:[[924],256],120732:[[925],256],120733:[[926],256],120734:[[927],256],120735:[[928],256],120736:[[929],256],120737:[[1012],256],120738:[[931],256],120739:[[932],256],120740:[[933],256],120741:[[934],256],120742:[[935],256],120743:[[936],256],120744:[[937],256],120745:[[8711],256],120746:[[945],256],120747:[[946],256],120748:[[947],256],120749:[[948],256],120750:[[949],256],120751:[[950],256],120752:[[951],256],120753:[[952],256],120754:[[953],256],120755:[[954],256],120756:[[955],256],120757:[[956],256],120758:[[957],256],120759:[[958],256],120760:[[959],256],120761:[[960],256],120762:[[961],256],120763:[[962],256],120764:[[963],256],120765:[[964],256],120766:[[965],256],120767:[[966],256],120768:[[967],256],120769:[[968],256],120770:[[969],256],120771:[[8706],256],120772:[[1013],256],120773:[[977],256],120774:[[1008],256],120775:[[981],256],120776:[[1009],256],120777:[[982],256],120778:[[988],256],120779:[[989],256],120782:[[48],256],120783:[[49],256],120784:[[50],256],120785:[[51],256],120786:[[52],256],120787:[[53],256],120788:[[54],256],120789:[[55],256],120790:[[56],256],120791:[[57],256],120792:[[48],256],120793:[[49],256],120794:[[50],256],120795:[[51],256],120796:[[52],256],120797:[[53],256],120798:[[54],256],120799:[[55],256],120800:[[56],256],120801:[[57],256],120802:[[48],256],120803:[[49],256],120804:[[50],256],120805:[[51],256],120806:[[52],256],120807:[[53],256],120808:[[54],256],120809:[[55],256],120810:[[56],256],120811:[[57],256],120812:[[48],256],120813:[[49],256],120814:[[50],256],120815:[[51],256],120816:[[52],256],120817:[[53],256],120818:[[54],256],120819:[[55],256],120820:[[56],256],120821:[[57],256],120822:[[48],256],120823:[[49],256],120824:[[50],256],120825:[[51],256],120826:[[52],256],120827:[[53],256],120828:[[54],256],120829:[[55],256],120830:[[56],256],120831:[[57],256]},
59392:{125136:[,220],125137:[,220],125138:[,220],125139:[,220],125140:[,220],125141:[,220],125142:[,220]},
60928:{126464:[[1575],256],126465:[[1576],256],126466:[[1580],256],126467:[[1583],256],126469:[[1608],256],126470:[[1586],256],126471:[[1581],256],126472:[[1591],256],126473:[[1610],256],126474:[[1603],256],126475:[[1604],256],126476:[[1605],256],126477:[[1606],256],126478:[[1587],256],126479:[[1593],256],126480:[[1601],256],126481:[[1589],256],126482:[[1602],256],126483:[[1585],256],126484:[[1588],256],126485:[[1578],256],126486:[[1579],256],126487:[[1582],256],126488:[[1584],256],126489:[[1590],256],126490:[[1592],256],126491:[[1594],256],126492:[[1646],256],126493:[[1722],256],126494:[[1697],256],126495:[[1647],256],126497:[[1576],256],126498:[[1580],256],126500:[[1607],256],126503:[[1581],256],126505:[[1610],256],126506:[[1603],256],126507:[[1604],256],126508:[[1605],256],126509:[[1606],256],126510:[[1587],256],126511:[[1593],256],126512:[[1601],256],126513:[[1589],256],126514:[[1602],256],126516:[[1588],256],126517:[[1578],256],126518:[[1579],256],126519:[[1582],256],126521:[[1590],256],126523:[[1594],256],126530:[[1580],256],126535:[[1581],256],126537:[[1610],256],126539:[[1604],256],126541:[[1606],256],126542:[[1587],256],126543:[[1593],256],126545:[[1589],256],126546:[[1602],256],126548:[[1588],256],126551:[[1582],256],126553:[[1590],256],126555:[[1594],256],126557:[[1722],256],126559:[[1647],256],126561:[[1576],256],126562:[[1580],256],126564:[[1607],256],126567:[[1581],256],126568:[[1591],256],126569:[[1610],256],126570:[[1603],256],126572:[[1605],256],126573:[[1606],256],126574:[[1587],256],126575:[[1593],256],126576:[[1601],256],126577:[[1589],256],126578:[[1602],256],126580:[[1588],256],126581:[[1578],256],126582:[[1579],256],126583:[[1582],256],126585:[[1590],256],126586:[[1592],256],126587:[[1594],256],126588:[[1646],256],126590:[[1697],256],126592:[[1575],256],126593:[[1576],256],126594:[[1580],256],126595:[[1583],256],126596:[[1607],256],126597:[[1608],256],126598:[[1586],256],126599:[[1581],256],126600:[[1591],256],126601:[[1610],256],126603:[[1604],256],126604:[[1605],256],126605:[[1606],256],126606:[[1587],256],126607:[[1593],256],126608:[[1601],256],126609:[[1589],256],126610:[[1602],256],126611:[[1585],256],126612:[[1588],256],126613:[[1578],256],126614:[[1579],256],126615:[[1582],256],126616:[[1584],256],126617:[[1590],256],126618:[[1592],256],126619:[[1594],256],126625:[[1576],256],126626:[[1580],256],126627:[[1583],256],126629:[[1608],256],126630:[[1586],256],126631:[[1581],256],126632:[[1591],256],126633:[[1610],256],126635:[[1604],256],126636:[[1605],256],126637:[[1606],256],126638:[[1587],256],126639:[[1593],256],126640:[[1601],256],126641:[[1589],256],126642:[[1602],256],126643:[[1585],256],126644:[[1588],256],126645:[[1578],256],126646:[[1579],256],126647:[[1582],256],126648:[[1584],256],126649:[[1590],256],126650:[[1592],256],126651:[[1594],256]},
61696:{127232:[[48,46],256],127233:[[48,44],256],127234:[[49,44],256],127235:[[50,44],256],127236:[[51,44],256],127237:[[52,44],256],127238:[[53,44],256],127239:[[54,44],256],127240:[[55,44],256],127241:[[56,44],256],127242:[[57,44],256],127248:[[40,65,41],256],127249:[[40,66,41],256],127250:[[40,67,41],256],127251:[[40,68,41],256],127252:[[40,69,41],256],127253:[[40,70,41],256],127254:[[40,71,41],256],127255:[[40,72,41],256],127256:[[40,73,41],256],127257:[[40,74,41],256],127258:[[40,75,41],256],127259:[[40,76,41],256],127260:[[40,77,41],256],127261:[[40,78,41],256],127262:[[40,79,41],256],127263:[[40,80,41],256],127264:[[40,81,41],256],127265:[[40,82,41],256],127266:[[40,83,41],256],127267:[[40,84,41],256],127268:[[40,85,41],256],127269:[[40,86,41],256],127270:[[40,87,41],256],127271:[[40,88,41],256],127272:[[40,89,41],256],127273:[[40,90,41],256],127274:[[12308,83,12309],256],127275:[[67],256],127276:[[82],256],127277:[[67,68],256],127278:[[87,90],256],127280:[[65],256],127281:[[66],256],127282:[[67],256],127283:[[68],256],127284:[[69],256],127285:[[70],256],127286:[[71],256],127287:[[72],256],127288:[[73],256],127289:[[74],256],127290:[[75],256],127291:[[76],256],127292:[[77],256],127293:[[78],256],127294:[[79],256],127295:[[80],256],127296:[[81],256],127297:[[82],256],127298:[[83],256],127299:[[84],256],127300:[[85],256],127301:[[86],256],127302:[[87],256],127303:[[88],256],127304:[[89],256],127305:[[90],256],127306:[[72,86],256],127307:[[77,86],256],127308:[[83,68],256],127309:[[83,83],256],127310:[[80,80,86],256],127311:[[87,67],256],127338:[[77,67],256],127339:[[77,68],256],127376:[[68,74],256]},
61952:{127488:[[12411,12363],256],127489:[[12467,12467],256],127490:[[12469],256],127504:[[25163],256],127505:[[23383],256],127506:[[21452],256],127507:[[12487],256],127508:[[20108],256],127509:[[22810],256],127510:[[35299],256],127511:[[22825],256],127512:[[20132],256],127513:[[26144],256],127514:[[28961],256],127515:[[26009],256],127516:[[21069],256],127517:[[24460],256],127518:[[20877],256],127519:[[26032],256],127520:[[21021],256],127521:[[32066],256],127522:[[29983],256],127523:[[36009],256],127524:[[22768],256],127525:[[21561],256],127526:[[28436],256],127527:[[25237],256],127528:[[25429],256],127529:[[19968],256],127530:[[19977],256],127531:[[36938],256],127532:[[24038],256],127533:[[20013],256],127534:[[21491],256],127535:[[25351],256],127536:[[36208],256],127537:[[25171],256],127538:[[31105],256],127539:[[31354],256],127540:[[21512],256],127541:[[28288],256],127542:[[26377],256],127543:[[26376],256],127544:[[30003],256],127545:[[21106],256],127546:[[21942],256],127552:[[12308,26412,12309],256],127553:[[12308,19977,12309],256],127554:[[12308,20108,12309],256],127555:[[12308,23433,12309],256],127556:[[12308,28857,12309],256],127557:[[12308,25171,12309],256],127558:[[12308,30423,12309],256],127559:[[12308,21213,12309],256],127560:[[12308,25943,12309],256],127568:[[24471],256],127569:[[21487],256]},
63488:{194560:[[20029]],194561:[[20024]],194562:[[20033]],194563:[[131362]],194564:[[20320]],194565:[[20398]],194566:[[20411]],194567:[[20482]],194568:[[20602]],194569:[[20633]],194570:[[20711]],194571:[[20687]],194572:[[13470]],194573:[[132666]],194574:[[20813]],194575:[[20820]],194576:[[20836]],194577:[[20855]],194578:[[132380]],194579:[[13497]],194580:[[20839]],194581:[[20877]],194582:[[132427]],194583:[[20887]],194584:[[20900]],194585:[[20172]],194586:[[20908]],194587:[[20917]],194588:[[168415]],194589:[[20981]],194590:[[20995]],194591:[[13535]],194592:[[21051]],194593:[[21062]],194594:[[21106]],194595:[[21111]],194596:[[13589]],194597:[[21191]],194598:[[21193]],194599:[[21220]],194600:[[21242]],194601:[[21253]],194602:[[21254]],194603:[[21271]],194604:[[21321]],194605:[[21329]],194606:[[21338]],194607:[[21363]],194608:[[21373]],194609:[[21375]],194610:[[21375]],194611:[[21375]],194612:[[133676]],194613:[[28784]],194614:[[21450]],194615:[[21471]],194616:[[133987]],194617:[[21483]],194618:[[21489]],194619:[[21510]],194620:[[21662]],194621:[[21560]],194622:[[21576]],194623:[[21608]],194624:[[21666]],194625:[[21750]],194626:[[21776]],194627:[[21843]],194628:[[21859]],194629:[[21892]],194630:[[21892]],194631:[[21913]],194632:[[21931]],194633:[[21939]],194634:[[21954]],194635:[[22294]],194636:[[22022]],194637:[[22295]],194638:[[22097]],194639:[[22132]],194640:[[20999]],194641:[[22766]],194642:[[22478]],194643:[[22516]],194644:[[22541]],194645:[[22411]],194646:[[22578]],194647:[[22577]],194648:[[22700]],194649:[[136420]],194650:[[22770]],194651:[[22775]],194652:[[22790]],194653:[[22810]],194654:[[22818]],194655:[[22882]],194656:[[136872]],194657:[[136938]],194658:[[23020]],194659:[[23067]],194660:[[23079]],194661:[[23000]],194662:[[23142]],194663:[[14062]],194664:[[14076]],194665:[[23304]],194666:[[23358]],194667:[[23358]],194668:[[137672]],194669:[[23491]],194670:[[23512]],194671:[[23527]],194672:[[23539]],194673:[[138008]],194674:[[23551]],194675:[[23558]],194676:[[24403]],194677:[[23586]],194678:[[14209]],194679:[[23648]],194680:[[23662]],194681:[[23744]],194682:[[23693]],194683:[[138724]],194684:[[23875]],194685:[[138726]],194686:[[23918]],194687:[[23915]],194688:[[23932]],194689:[[24033]],194690:[[24034]],194691:[[14383]],194692:[[24061]],194693:[[24104]],194694:[[24125]],194695:[[24169]],194696:[[14434]],194697:[[139651]],194698:[[14460]],194699:[[24240]],194700:[[24243]],194701:[[24246]],194702:[[24266]],194703:[[172946]],194704:[[24318]],194705:[[140081]],194706:[[140081]],194707:[[33281]],194708:[[24354]],194709:[[24354]],194710:[[14535]],194711:[[144056]],194712:[[156122]],194713:[[24418]],194714:[[24427]],194715:[[14563]],194716:[[24474]],194717:[[24525]],194718:[[24535]],194719:[[24569]],194720:[[24705]],194721:[[14650]],194722:[[14620]],194723:[[24724]],194724:[[141012]],194725:[[24775]],194726:[[24904]],194727:[[24908]],194728:[[24910]],194729:[[24908]],194730:[[24954]],194731:[[24974]],194732:[[25010]],194733:[[24996]],194734:[[25007]],194735:[[25054]],194736:[[25074]],194737:[[25078]],194738:[[25104]],194739:[[25115]],194740:[[25181]],194741:[[25265]],194742:[[25300]],194743:[[25424]],194744:[[142092]],194745:[[25405]],194746:[[25340]],194747:[[25448]],194748:[[25475]],194749:[[25572]],194750:[[142321]],194751:[[25634]],194752:[[25541]],194753:[[25513]],194754:[[14894]],194755:[[25705]],194756:[[25726]],194757:[[25757]],194758:[[25719]],194759:[[14956]],194760:[[25935]],194761:[[25964]],194762:[[143370]],194763:[[26083]],194764:[[26360]],194765:[[26185]],194766:[[15129]],194767:[[26257]],194768:[[15112]],194769:[[15076]],194770:[[20882]],194771:[[20885]],194772:[[26368]],194773:[[26268]],194774:[[32941]],194775:[[17369]],194776:[[26391]],194777:[[26395]],194778:[[26401]],194779:[[26462]],194780:[[26451]],194781:[[144323]],194782:[[15177]],194783:[[26618]],194784:[[26501]],194785:[[26706]],194786:[[26757]],194787:[[144493]],194788:[[26766]],194789:[[26655]],194790:[[26900]],194791:[[15261]],194792:[[26946]],194793:[[27043]],194794:[[27114]],194795:[[27304]],194796:[[145059]],194797:[[27355]],194798:[[15384]],194799:[[27425]],194800:[[145575]],194801:[[27476]],194802:[[15438]],194803:[[27506]],194804:[[27551]],194805:[[27578]],194806:[[27579]],194807:[[146061]],194808:[[138507]],194809:[[146170]],194810:[[27726]],194811:[[146620]],194812:[[27839]],194813:[[27853]],194814:[[27751]],194815:[[27926]]},
63744:{63744:[[35912]],63745:[[26356]],63746:[[36554]],63747:[[36040]],63748:[[28369]],63749:[[20018]],63750:[[21477]],63751:[[40860]],63752:[[40860]],63753:[[22865]],63754:[[37329]],63755:[[21895]],63756:[[22856]],63757:[[25078]],63758:[[30313]],63759:[[32645]],63760:[[34367]],63761:[[34746]],63762:[[35064]],63763:[[37007]],63764:[[27138]],63765:[[27931]],63766:[[28889]],63767:[[29662]],63768:[[33853]],63769:[[37226]],63770:[[39409]],63771:[[20098]],63772:[[21365]],63773:[[27396]],63774:[[29211]],63775:[[34349]],63776:[[40478]],63777:[[23888]],63778:[[28651]],63779:[[34253]],63780:[[35172]],63781:[[25289]],63782:[[33240]],63783:[[34847]],63784:[[24266]],63785:[[26391]],63786:[[28010]],63787:[[29436]],63788:[[37070]],63789:[[20358]],63790:[[20919]],63791:[[21214]],63792:[[25796]],63793:[[27347]],63794:[[29200]],63795:[[30439]],63796:[[32769]],63797:[[34310]],63798:[[34396]],63799:[[36335]],63800:[[38706]],63801:[[39791]],63802:[[40442]],63803:[[30860]],63804:[[31103]],63805:[[32160]],63806:[[33737]],63807:[[37636]],63808:[[40575]],63809:[[35542]],63810:[[22751]],63811:[[24324]],63812:[[31840]],63813:[[32894]],63814:[[29282]],63815:[[30922]],63816:[[36034]],63817:[[38647]],63818:[[22744]],63819:[[23650]],63820:[[27155]],63821:[[28122]],63822:[[28431]],63823:[[32047]],63824:[[32311]],63825:[[38475]],63826:[[21202]],63827:[[32907]],63828:[[20956]],63829:[[20940]],63830:[[31260]],63831:[[32190]],63832:[[33777]],63833:[[38517]],63834:[[35712]],63835:[[25295]],63836:[[27138]],63837:[[35582]],63838:[[20025]],63839:[[23527]],63840:[[24594]],63841:[[29575]],63842:[[30064]],63843:[[21271]],63844:[[30971]],63845:[[20415]],63846:[[24489]],63847:[[19981]],63848:[[27852]],63849:[[25976]],63850:[[32034]],63851:[[21443]],63852:[[22622]],63853:[[30465]],63854:[[33865]],63855:[[35498]],63856:[[27578]],63857:[[36784]],63858:[[27784]],63859:[[25342]],63860:[[33509]],63861:[[25504]],63862:[[30053]],63863:[[20142]],63864:[[20841]],63865:[[20937]],63866:[[26753]],63867:[[31975]],63868:[[33391]],63869:[[35538]],63870:[[37327]],63871:[[21237]],63872:[[21570]],63873:[[22899]],63874:[[24300]],63875:[[26053]],63876:[[28670]],63877:[[31018]],63878:[[38317]],63879:[[39530]],63880:[[40599]],63881:[[40654]],63882:[[21147]],63883:[[26310]],63884:[[27511]],63885:[[36706]],63886:[[24180]],63887:[[24976]],63888:[[25088]],63889:[[25754]],63890:[[28451]],63891:[[29001]],63892:[[29833]],63893:[[31178]],63894:[[32244]],63895:[[32879]],63896:[[36646]],63897:[[34030]],63898:[[36899]],63899:[[37706]],63900:[[21015]],63901:[[21155]],63902:[[21693]],63903:[[28872]],63904:[[35010]],63905:[[35498]],63906:[[24265]],63907:[[24565]],63908:[[25467]],63909:[[27566]],63910:[[31806]],63911:[[29557]],63912:[[20196]],63913:[[22265]],63914:[[23527]],63915:[[23994]],63916:[[24604]],63917:[[29618]],63918:[[29801]],63919:[[32666]],63920:[[32838]],63921:[[37428]],63922:[[38646]],63923:[[38728]],63924:[[38936]],63925:[[20363]],63926:[[31150]],63927:[[37300]],63928:[[38584]],63929:[[24801]],63930:[[20102]],63931:[[20698]],63932:[[23534]],63933:[[23615]],63934:[[26009]],63935:[[27138]],63936:[[29134]],63937:[[30274]],63938:[[34044]],63939:[[36988]],63940:[[40845]],63941:[[26248]],63942:[[38446]],63943:[[21129]],63944:[[26491]],63945:[[26611]],63946:[[27969]],63947:[[28316]],63948:[[29705]],63949:[[30041]],63950:[[30827]],63951:[[32016]],63952:[[39006]],63953:[[20845]],63954:[[25134]],63955:[[38520]],63956:[[20523]],63957:[[23833]],63958:[[28138]],63959:[[36650]],63960:[[24459]],63961:[[24900]],63962:[[26647]],63963:[[29575]],63964:[[38534]],63965:[[21033]],63966:[[21519]],63967:[[23653]],63968:[[26131]],63969:[[26446]],63970:[[26792]],63971:[[27877]],63972:[[29702]],63973:[[30178]],63974:[[32633]],63975:[[35023]],63976:[[35041]],63977:[[37324]],63978:[[38626]],63979:[[21311]],63980:[[28346]],63981:[[21533]],63982:[[29136]],63983:[[29848]],63984:[[34298]],63985:[[38563]],63986:[[40023]],63987:[[40607]],63988:[[26519]],63989:[[28107]],63990:[[33256]],63991:[[31435]],63992:[[31520]],63993:[[31890]],63994:[[29376]],63995:[[28825]],63996:[[35672]],63997:[[20160]],63998:[[33590]],63999:[[21050]],194816:[[27966]],194817:[[28023]],194818:[[27969]],194819:[[28009]],194820:[[28024]],194821:[[28037]],194822:[[146718]],194823:[[27956]],194824:[[28207]],194825:[[28270]],194826:[[15667]],194827:[[28363]],194828:[[28359]],194829:[[147153]],194830:[[28153]],194831:[[28526]],194832:[[147294]],194833:[[147342]],194834:[[28614]],194835:[[28729]],194836:[[28702]],194837:[[28699]],194838:[[15766]],194839:[[28746]],194840:[[28797]],194841:[[28791]],194842:[[28845]],194843:[[132389]],194844:[[28997]],194845:[[148067]],194846:[[29084]],194847:[[148395]],194848:[[29224]],194849:[[29237]],194850:[[29264]],194851:[[149000]],194852:[[29312]],194853:[[29333]],194854:[[149301]],194855:[[149524]],194856:[[29562]],194857:[[29579]],194858:[[16044]],194859:[[29605]],194860:[[16056]],194861:[[16056]],194862:[[29767]],194863:[[29788]],194864:[[29809]],194865:[[29829]],194866:[[29898]],194867:[[16155]],194868:[[29988]],194869:[[150582]],194870:[[30014]],194871:[[150674]],194872:[[30064]],194873:[[139679]],194874:[[30224]],194875:[[151457]],194876:[[151480]],194877:[[151620]],194878:[[16380]],194879:[[16392]],194880:[[30452]],194881:[[151795]],194882:[[151794]],194883:[[151833]],194884:[[151859]],194885:[[30494]],194886:[[30495]],194887:[[30495]],194888:[[30538]],194889:[[16441]],194890:[[30603]],194891:[[16454]],194892:[[16534]],194893:[[152605]],194894:[[30798]],194895:[[30860]],194896:[[30924]],194897:[[16611]],194898:[[153126]],194899:[[31062]],194900:[[153242]],194901:[[153285]],194902:[[31119]],194903:[[31211]],194904:[[16687]],194905:[[31296]],194906:[[31306]],194907:[[31311]],194908:[[153980]],194909:[[154279]],194910:[[154279]],194911:[[31470]],194912:[[16898]],194913:[[154539]],194914:[[31686]],194915:[[31689]],194916:[[16935]],194917:[[154752]],194918:[[31954]],194919:[[17056]],194920:[[31976]],194921:[[31971]],194922:[[32000]],194923:[[155526]],194924:[[32099]],194925:[[17153]],194926:[[32199]],194927:[[32258]],194928:[[32325]],194929:[[17204]],194930:[[156200]],194931:[[156231]],194932:[[17241]],194933:[[156377]],194934:[[32634]],194935:[[156478]],194936:[[32661]],194937:[[32762]],194938:[[32773]],194939:[[156890]],194940:[[156963]],194941:[[32864]],194942:[[157096]],194943:[[32880]],194944:[[144223]],194945:[[17365]],194946:[[32946]],194947:[[33027]],194948:[[17419]],194949:[[33086]],194950:[[23221]],194951:[[157607]],194952:[[157621]],194953:[[144275]],194954:[[144284]],194955:[[33281]],194956:[[33284]],194957:[[36766]],194958:[[17515]],194959:[[33425]],194960:[[33419]],194961:[[33437]],194962:[[21171]],194963:[[33457]],194964:[[33459]],194965:[[33469]],194966:[[33510]],194967:[[158524]],194968:[[33509]],194969:[[33565]],194970:[[33635]],194971:[[33709]],194972:[[33571]],194973:[[33725]],194974:[[33767]],194975:[[33879]],194976:[[33619]],194977:[[33738]],194978:[[33740]],194979:[[33756]],194980:[[158774]],194981:[[159083]],194982:[[158933]],194983:[[17707]],194984:[[34033]],194985:[[34035]],194986:[[34070]],194987:[[160714]],194988:[[34148]],194989:[[159532]],194990:[[17757]],194991:[[17761]],194992:[[159665]],194993:[[159954]],194994:[[17771]],194995:[[34384]],194996:[[34396]],194997:[[34407]],194998:[[34409]],194999:[[34473]],195000:[[34440]],195001:[[34574]],195002:[[34530]],195003:[[34681]],195004:[[34600]],195005:[[34667]],195006:[[34694]],195007:[[17879]],195008:[[34785]],195009:[[34817]],195010:[[17913]],195011:[[34912]],195012:[[34915]],195013:[[161383]],195014:[[35031]],195015:[[35038]],195016:[[17973]],195017:[[35066]],195018:[[13499]],195019:[[161966]],195020:[[162150]],195021:[[18110]],195022:[[18119]],195023:[[35488]],195024:[[35565]],195025:[[35722]],195026:[[35925]],195027:[[162984]],195028:[[36011]],195029:[[36033]],195030:[[36123]],195031:[[36215]],195032:[[163631]],195033:[[133124]],195034:[[36299]],195035:[[36284]],195036:[[36336]],195037:[[133342]],195038:[[36564]],195039:[[36664]],195040:[[165330]],195041:[[165357]],195042:[[37012]],195043:[[37105]],195044:[[37137]],195045:[[165678]],195046:[[37147]],195047:[[37432]],195048:[[37591]],195049:[[37592]],195050:[[37500]],195051:[[37881]],195052:[[37909]],195053:[[166906]],195054:[[38283]],195055:[[18837]],195056:[[38327]],195057:[[167287]],195058:[[18918]],195059:[[38595]],195060:[[23986]],195061:[[38691]],195062:[[168261]],195063:[[168474]],195064:[[19054]],195065:[[19062]],195066:[[38880]],195067:[[168970]],195068:[[19122]],195069:[[169110]],195070:[[38923]],195071:[[38923]]},
64000:{64000:[[20999]],64001:[[24230]],64002:[[25299]],64003:[[31958]],64004:[[23429]],64005:[[27934]],64006:[[26292]],64007:[[36667]],64008:[[34892]],64009:[[38477]],64010:[[35211]],64011:[[24275]],64012:[[20800]],64013:[[21952]],64016:[[22618]],64018:[[26228]],64021:[[20958]],64022:[[29482]],64023:[[30410]],64024:[[31036]],64025:[[31070]],64026:[[31077]],64027:[[31119]],64028:[[38742]],64029:[[31934]],64030:[[32701]],64032:[[34322]],64034:[[35576]],64037:[[36920]],64038:[[37117]],64042:[[39151]],64043:[[39164]],64044:[[39208]],64045:[[40372]],64046:[[37086]],64047:[[38583]],64048:[[20398]],64049:[[20711]],64050:[[20813]],64051:[[21193]],64052:[[21220]],64053:[[21329]],64054:[[21917]],64055:[[22022]],64056:[[22120]],64057:[[22592]],64058:[[22696]],64059:[[23652]],64060:[[23662]],64061:[[24724]],64062:[[24936]],64063:[[24974]],64064:[[25074]],64065:[[25935]],64066:[[26082]],64067:[[26257]],64068:[[26757]],64069:[[28023]],64070:[[28186]],64071:[[28450]],64072:[[29038]],64073:[[29227]],64074:[[29730]],64075:[[30865]],64076:[[31038]],64077:[[31049]],64078:[[31048]],64079:[[31056]],64080:[[31062]],64081:[[31069]],64082:[[31117]],64083:[[31118]],64084:[[31296]],64085:[[31361]],64086:[[31680]],64087:[[32244]],64088:[[32265]],64089:[[32321]],64090:[[32626]],64091:[[32773]],64092:[[33261]],64093:[[33401]],64094:[[33401]],64095:[[33879]],64096:[[35088]],64097:[[35222]],64098:[[35585]],64099:[[35641]],64100:[[36051]],64101:[[36104]],64102:[[36790]],64103:[[36920]],64104:[[38627]],64105:[[38911]],64106:[[38971]],64107:[[24693]],64108:[[148206]],64109:[[33304]],64112:[[20006]],64113:[[20917]],64114:[[20840]],64115:[[20352]],64116:[[20805]],64117:[[20864]],64118:[[21191]],64119:[[21242]],64120:[[21917]],64121:[[21845]],64122:[[21913]],64123:[[21986]],64124:[[22618]],64125:[[22707]],64126:[[22852]],64127:[[22868]],64128:[[23138]],64129:[[23336]],64130:[[24274]],64131:[[24281]],64132:[[24425]],64133:[[24493]],64134:[[24792]],64135:[[24910]],64136:[[24840]],64137:[[24974]],64138:[[24928]],64139:[[25074]],64140:[[25140]],64141:[[25540]],64142:[[25628]],64143:[[25682]],64144:[[25942]],64145:[[26228]],64146:[[26391]],64147:[[26395]],64148:[[26454]],64149:[[27513]],64150:[[27578]],64151:[[27969]],64152:[[28379]],64153:[[28363]],64154:[[28450]],64155:[[28702]],64156:[[29038]],64157:[[30631]],64158:[[29237]],64159:[[29359]],64160:[[29482]],64161:[[29809]],64162:[[29958]],64163:[[30011]],64164:[[30237]],64165:[[30239]],64166:[[30410]],64167:[[30427]],64168:[[30452]],64169:[[30538]],64170:[[30528]],64171:[[30924]],64172:[[31409]],64173:[[31680]],64174:[[31867]],64175:[[32091]],64176:[[32244]],64177:[[32574]],64178:[[32773]],64179:[[33618]],64180:[[33775]],64181:[[34681]],64182:[[35137]],64183:[[35206]],64184:[[35222]],64185:[[35519]],64186:[[35576]],64187:[[35531]],64188:[[35585]],64189:[[35582]],64190:[[35565]],64191:[[35641]],64192:[[35722]],64193:[[36104]],64194:[[36664]],64195:[[36978]],64196:[[37273]],64197:[[37494]],64198:[[38524]],64199:[[38627]],64200:[[38742]],64201:[[38875]],64202:[[38911]],64203:[[38923]],64204:[[38971]],64205:[[39698]],64206:[[40860]],64207:[[141386]],64208:[[141380]],64209:[[144341]],64210:[[15261]],64211:[[16408]],64212:[[16441]],64213:[[152137]],64214:[[154832]],64215:[[163539]],64216:[[40771]],64217:[[40846]],195072:[[38953]],195073:[[169398]],195074:[[39138]],195075:[[19251]],195076:[[39209]],195077:[[39335]],195078:[[39362]],195079:[[39422]],195080:[[19406]],195081:[[170800]],195082:[[39698]],195083:[[40000]],195084:[[40189]],195085:[[19662]],195086:[[19693]],195087:[[40295]],195088:[[172238]],195089:[[19704]],195090:[[172293]],195091:[[172558]],195092:[[172689]],195093:[[40635]],195094:[[19798]],195095:[[40697]],195096:[[40702]],195097:[[40709]],195098:[[40719]],195099:[[40726]],195100:[[40763]],195101:[[173568]]},
64256:{64256:[[102,102],256],64257:[[102,105],256],64258:[[102,108],256],64259:[[102,102,105],256],64260:[[102,102,108],256],64261:[[383,116],256],64262:[[115,116],256],64275:[[1396,1398],256],64276:[[1396,1381],256],64277:[[1396,1387],256],64278:[[1406,1398],256],64279:[[1396,1389],256],64285:[[1497,1460],512],64286:[,26],64287:[[1522,1463],512],64288:[[1506],256],64289:[[1488],256],64290:[[1491],256],64291:[[1492],256],64292:[[1499],256],64293:[[1500],256],64294:[[1501],256],64295:[[1512],256],64296:[[1514],256],64297:[[43],256],64298:[[1513,1473],512],64299:[[1513,1474],512],64300:[[64329,1473],512],64301:[[64329,1474],512],64302:[[1488,1463],512],64303:[[1488,1464],512],64304:[[1488,1468],512],64305:[[1489,1468],512],64306:[[1490,1468],512],64307:[[1491,1468],512],64308:[[1492,1468],512],64309:[[1493,1468],512],64310:[[1494,1468],512],64312:[[1496,1468],512],64313:[[1497,1468],512],64314:[[1498,1468],512],64315:[[1499,1468],512],64316:[[1500,1468],512],64318:[[1502,1468],512],64320:[[1504,1468],512],64321:[[1505,1468],512],64323:[[1507,1468],512],64324:[[1508,1468],512],64326:[[1510,1468],512],64327:[[1511,1468],512],64328:[[1512,1468],512],64329:[[1513,1468],512],64330:[[1514,1468],512],64331:[[1493,1465],512],64332:[[1489,1471],512],64333:[[1499,1471],512],64334:[[1508,1471],512],64335:[[1488,1500],256],64336:[[1649],256],64337:[[1649],256],64338:[[1659],256],64339:[[1659],256],64340:[[1659],256],64341:[[1659],256],64342:[[1662],256],64343:[[1662],256],64344:[[1662],256],64345:[[1662],256],64346:[[1664],256],64347:[[1664],256],64348:[[1664],256],64349:[[1664],256],64350:[[1658],256],64351:[[1658],256],64352:[[1658],256],64353:[[1658],256],64354:[[1663],256],64355:[[1663],256],64356:[[1663],256],64357:[[1663],256],64358:[[1657],256],64359:[[1657],256],64360:[[1657],256],64361:[[1657],256],64362:[[1700],256],64363:[[1700],256],64364:[[1700],256],64365:[[1700],256],64366:[[1702],256],64367:[[1702],256],64368:[[1702],256],64369:[[1702],256],64370:[[1668],256],64371:[[1668],256],64372:[[1668],256],64373:[[1668],256],64374:[[1667],256],64375:[[1667],256],64376:[[1667],256],64377:[[1667],256],64378:[[1670],256],64379:[[1670],256],64380:[[1670],256],64381:[[1670],256],64382:[[1671],256],64383:[[1671],256],64384:[[1671],256],64385:[[1671],256],64386:[[1677],256],64387:[[1677],256],64388:[[1676],256],64389:[[1676],256],64390:[[1678],256],64391:[[1678],256],64392:[[1672],256],64393:[[1672],256],64394:[[1688],256],64395:[[1688],256],64396:[[1681],256],64397:[[1681],256],64398:[[1705],256],64399:[[1705],256],64400:[[1705],256],64401:[[1705],256],64402:[[1711],256],64403:[[1711],256],64404:[[1711],256],64405:[[1711],256],64406:[[1715],256],64407:[[1715],256],64408:[[1715],256],64409:[[1715],256],64410:[[1713],256],64411:[[1713],256],64412:[[1713],256],64413:[[1713],256],64414:[[1722],256],64415:[[1722],256],64416:[[1723],256],64417:[[1723],256],64418:[[1723],256],64419:[[1723],256],64420:[[1728],256],64421:[[1728],256],64422:[[1729],256],64423:[[1729],256],64424:[[1729],256],64425:[[1729],256],64426:[[1726],256],64427:[[1726],256],64428:[[1726],256],64429:[[1726],256],64430:[[1746],256],64431:[[1746],256],64432:[[1747],256],64433:[[1747],256],64467:[[1709],256],64468:[[1709],256],64469:[[1709],256],64470:[[1709],256],64471:[[1735],256],64472:[[1735],256],64473:[[1734],256],64474:[[1734],256],64475:[[1736],256],64476:[[1736],256],64477:[[1655],256],64478:[[1739],256],64479:[[1739],256],64480:[[1733],256],64481:[[1733],256],64482:[[1737],256],64483:[[1737],256],64484:[[1744],256],64485:[[1744],256],64486:[[1744],256],64487:[[1744],256],64488:[[1609],256],64489:[[1609],256],64490:[[1574,1575],256],64491:[[1574,1575],256],64492:[[1574,1749],256],64493:[[1574,1749],256],64494:[[1574,1608],256],64495:[[1574,1608],256],64496:[[1574,1735],256],64497:[[1574,1735],256],64498:[[1574,1734],256],64499:[[1574,1734],256],64500:[[1574,1736],256],64501:[[1574,1736],256],64502:[[1574,1744],256],64503:[[1574,1744],256],64504:[[1574,1744],256],64505:[[1574,1609],256],64506:[[1574,1609],256],64507:[[1574,1609],256],64508:[[1740],256],64509:[[1740],256],64510:[[1740],256],64511:[[1740],256]},
64512:{64512:[[1574,1580],256],64513:[[1574,1581],256],64514:[[1574,1605],256],64515:[[1574,1609],256],64516:[[1574,1610],256],64517:[[1576,1580],256],64518:[[1576,1581],256],64519:[[1576,1582],256],64520:[[1576,1605],256],64521:[[1576,1609],256],64522:[[1576,1610],256],64523:[[1578,1580],256],64524:[[1578,1581],256],64525:[[1578,1582],256],64526:[[1578,1605],256],64527:[[1578,1609],256],64528:[[1578,1610],256],64529:[[1579,1580],256],64530:[[1579,1605],256],64531:[[1579,1609],256],64532:[[1579,1610],256],64533:[[1580,1581],256],64534:[[1580,1605],256],64535:[[1581,1580],256],64536:[[1581,1605],256],64537:[[1582,1580],256],64538:[[1582,1581],256],64539:[[1582,1605],256],64540:[[1587,1580],256],64541:[[1587,1581],256],64542:[[1587,1582],256],64543:[[1587,1605],256],64544:[[1589,1581],256],64545:[[1589,1605],256],64546:[[1590,1580],256],64547:[[1590,1581],256],64548:[[1590,1582],256],64549:[[1590,1605],256],64550:[[1591,1581],256],64551:[[1591,1605],256],64552:[[1592,1605],256],64553:[[1593,1580],256],64554:[[1593,1605],256],64555:[[1594,1580],256],64556:[[1594,1605],256],64557:[[1601,1580],256],64558:[[1601,1581],256],64559:[[1601,1582],256],64560:[[1601,1605],256],64561:[[1601,1609],256],64562:[[1601,1610],256],64563:[[1602,1581],256],64564:[[1602,1605],256],64565:[[1602,1609],256],64566:[[1602,1610],256],64567:[[1603,1575],256],64568:[[1603,1580],256],64569:[[1603,1581],256],64570:[[1603,1582],256],64571:[[1603,1604],256],64572:[[1603,1605],256],64573:[[1603,1609],256],64574:[[1603,1610],256],64575:[[1604,1580],256],64576:[[1604,1581],256],64577:[[1604,1582],256],64578:[[1604,1605],256],64579:[[1604,1609],256],64580:[[1604,1610],256],64581:[[1605,1580],256],64582:[[1605,1581],256],64583:[[1605,1582],256],64584:[[1605,1605],256],64585:[[1605,1609],256],64586:[[1605,1610],256],64587:[[1606,1580],256],64588:[[1606,1581],256],64589:[[1606,1582],256],64590:[[1606,1605],256],64591:[[1606,1609],256],64592:[[1606,1610],256],64593:[[1607,1580],256],64594:[[1607,1605],256],64595:[[1607,1609],256],64596:[[1607,1610],256],64597:[[1610,1580],256],64598:[[1610,1581],256],64599:[[1610,1582],256],64600:[[1610,1605],256],64601:[[1610,1609],256],64602:[[1610,1610],256],64603:[[1584,1648],256],64604:[[1585,1648],256],64605:[[1609,1648],256],64606:[[32,1612,1617],256],64607:[[32,1613,1617],256],64608:[[32,1614,1617],256],64609:[[32,1615,1617],256],64610:[[32,1616,1617],256],64611:[[32,1617,1648],256],64612:[[1574,1585],256],64613:[[1574,1586],256],64614:[[1574,1605],256],64615:[[1574,1606],256],64616:[[1574,1609],256],64617:[[1574,1610],256],64618:[[1576,1585],256],64619:[[1576,1586],256],64620:[[1576,1605],256],64621:[[1576,1606],256],64622:[[1576,1609],256],64623:[[1576,1610],256],64624:[[1578,1585],256],64625:[[1578,1586],256],64626:[[1578,1605],256],64627:[[1578,1606],256],64628:[[1578,1609],256],64629:[[1578,1610],256],64630:[[1579,1585],256],64631:[[1579,1586],256],64632:[[1579,1605],256],64633:[[1579,1606],256],64634:[[1579,1609],256],64635:[[1579,1610],256],64636:[[1601,1609],256],64637:[[1601,1610],256],64638:[[1602,1609],256],64639:[[1602,1610],256],64640:[[1603,1575],256],64641:[[1603,1604],256],64642:[[1603,1605],256],64643:[[1603,1609],256],64644:[[1603,1610],256],64645:[[1604,1605],256],64646:[[1604,1609],256],64647:[[1604,1610],256],64648:[[1605,1575],256],64649:[[1605,1605],256],64650:[[1606,1585],256],64651:[[1606,1586],256],64652:[[1606,1605],256],64653:[[1606,1606],256],64654:[[1606,1609],256],64655:[[1606,1610],256],64656:[[1609,1648],256],64657:[[1610,1585],256],64658:[[1610,1586],256],64659:[[1610,1605],256],64660:[[1610,1606],256],64661:[[1610,1609],256],64662:[[1610,1610],256],64663:[[1574,1580],256],64664:[[1574,1581],256],64665:[[1574,1582],256],64666:[[1574,1605],256],64667:[[1574,1607],256],64668:[[1576,1580],256],64669:[[1576,1581],256],64670:[[1576,1582],256],64671:[[1576,1605],256],64672:[[1576,1607],256],64673:[[1578,1580],256],64674:[[1578,1581],256],64675:[[1578,1582],256],64676:[[1578,1605],256],64677:[[1578,1607],256],64678:[[1579,1605],256],64679:[[1580,1581],256],64680:[[1580,1605],256],64681:[[1581,1580],256],64682:[[1581,1605],256],64683:[[1582,1580],256],64684:[[1582,1605],256],64685:[[1587,1580],256],64686:[[1587,1581],256],64687:[[1587,1582],256],64688:[[1587,1605],256],64689:[[1589,1581],256],64690:[[1589,1582],256],64691:[[1589,1605],256],64692:[[1590,1580],256],64693:[[1590,1581],256],64694:[[1590,1582],256],64695:[[1590,1605],256],64696:[[1591,1581],256],64697:[[1592,1605],256],64698:[[1593,1580],256],64699:[[1593,1605],256],64700:[[1594,1580],256],64701:[[1594,1605],256],64702:[[1601,1580],256],64703:[[1601,1581],256],64704:[[1601,1582],256],64705:[[1601,1605],256],64706:[[1602,1581],256],64707:[[1602,1605],256],64708:[[1603,1580],256],64709:[[1603,1581],256],64710:[[1603,1582],256],64711:[[1603,1604],256],64712:[[1603,1605],256],64713:[[1604,1580],256],64714:[[1604,1581],256],64715:[[1604,1582],256],64716:[[1604,1605],256],64717:[[1604,1607],256],64718:[[1605,1580],256],64719:[[1605,1581],256],64720:[[1605,1582],256],64721:[[1605,1605],256],64722:[[1606,1580],256],64723:[[1606,1581],256],64724:[[1606,1582],256],64725:[[1606,1605],256],64726:[[1606,1607],256],64727:[[1607,1580],256],64728:[[1607,1605],256],64729:[[1607,1648],256],64730:[[1610,1580],256],64731:[[1610,1581],256],64732:[[1610,1582],256],64733:[[1610,1605],256],64734:[[1610,1607],256],64735:[[1574,1605],256],64736:[[1574,1607],256],64737:[[1576,1605],256],64738:[[1576,1607],256],64739:[[1578,1605],256],64740:[[1578,1607],256],64741:[[1579,1605],256],64742:[[1579,1607],256],64743:[[1587,1605],256],64744:[[1587,1607],256],64745:[[1588,1605],256],64746:[[1588,1607],256],64747:[[1603,1604],256],64748:[[1603,1605],256],64749:[[1604,1605],256],64750:[[1606,1605],256],64751:[[1606,1607],256],64752:[[1610,1605],256],64753:[[1610,1607],256],64754:[[1600,1614,1617],256],64755:[[1600,1615,1617],256],64756:[[1600,1616,1617],256],64757:[[1591,1609],256],64758:[[1591,1610],256],64759:[[1593,1609],256],64760:[[1593,1610],256],64761:[[1594,1609],256],64762:[[1594,1610],256],64763:[[1587,1609],256],64764:[[1587,1610],256],64765:[[1588,1609],256],64766:[[1588,1610],256],64767:[[1581,1609],256]},
64768:{64768:[[1581,1610],256],64769:[[1580,1609],256],64770:[[1580,1610],256],64771:[[1582,1609],256],64772:[[1582,1610],256],64773:[[1589,1609],256],64774:[[1589,1610],256],64775:[[1590,1609],256],64776:[[1590,1610],256],64777:[[1588,1580],256],64778:[[1588,1581],256],64779:[[1588,1582],256],64780:[[1588,1605],256],64781:[[1588,1585],256],64782:[[1587,1585],256],64783:[[1589,1585],256],64784:[[1590,1585],256],64785:[[1591,1609],256],64786:[[1591,1610],256],64787:[[1593,1609],256],64788:[[1593,1610],256],64789:[[1594,1609],256],64790:[[1594,1610],256],64791:[[1587,1609],256],64792:[[1587,1610],256],64793:[[1588,1609],256],64794:[[1588,1610],256],64795:[[1581,1609],256],64796:[[1581,1610],256],64797:[[1580,1609],256],64798:[[1580,1610],256],64799:[[1582,1609],256],64800:[[1582,1610],256],64801:[[1589,1609],256],64802:[[1589,1610],256],64803:[[1590,1609],256],64804:[[1590,1610],256],64805:[[1588,1580],256],64806:[[1588,1581],256],64807:[[1588,1582],256],64808:[[1588,1605],256],64809:[[1588,1585],256],64810:[[1587,1585],256],64811:[[1589,1585],256],64812:[[1590,1585],256],64813:[[1588,1580],256],64814:[[1588,1581],256],64815:[[1588,1582],256],64816:[[1588,1605],256],64817:[[1587,1607],256],64818:[[1588,1607],256],64819:[[1591,1605],256],64820:[[1587,1580],256],64821:[[1587,1581],256],64822:[[1587,1582],256],64823:[[1588,1580],256],64824:[[1588,1581],256],64825:[[1588,1582],256],64826:[[1591,1605],256],64827:[[1592,1605],256],64828:[[1575,1611],256],64829:[[1575,1611],256],64848:[[1578,1580,1605],256],64849:[[1578,1581,1580],256],64850:[[1578,1581,1580],256],64851:[[1578,1581,1605],256],64852:[[1578,1582,1605],256],64853:[[1578,1605,1580],256],64854:[[1578,1605,1581],256],64855:[[1578,1605,1582],256],64856:[[1580,1605,1581],256],64857:[[1580,1605,1581],256],64858:[[1581,1605,1610],256],64859:[[1581,1605,1609],256],64860:[[1587,1581,1580],256],64861:[[1587,1580,1581],256],64862:[[1587,1580,1609],256],64863:[[1587,1605,1581],256],64864:[[1587,1605,1581],256],64865:[[1587,1605,1580],256],64866:[[1587,1605,1605],256],64867:[[1587,1605,1605],256],64868:[[1589,1581,1581],256],64869:[[1589,1581,1581],256],64870:[[1589,1605,1605],256],64871:[[1588,1581,1605],256],64872:[[1588,1581,1605],256],64873:[[1588,1580,1610],256],64874:[[1588,1605,1582],256],64875:[[1588,1605,1582],256],64876:[[1588,1605,1605],256],64877:[[1588,1605,1605],256],64878:[[1590,1581,1609],256],64879:[[1590,1582,1605],256],64880:[[1590,1582,1605],256],64881:[[1591,1605,1581],256],64882:[[1591,1605,1581],256],64883:[[1591,1605,1605],256],64884:[[1591,1605,1610],256],64885:[[1593,1580,1605],256],64886:[[1593,1605,1605],256],64887:[[1593,1605,1605],256],64888:[[1593,1605,1609],256],64889:[[1594,1605,1605],256],64890:[[1594,1605,1610],256],64891:[[1594,1605,1609],256],64892:[[1601,1582,1605],256],64893:[[1601,1582,1605],256],64894:[[1602,1605,1581],256],64895:[[1602,1605,1605],256],64896:[[1604,1581,1605],256],64897:[[1604,1581,1610],256],64898:[[1604,1581,1609],256],64899:[[1604,1580,1580],256],64900:[[1604,1580,1580],256],64901:[[1604,1582,1605],256],64902:[[1604,1582,1605],256],64903:[[1604,1605,1581],256],64904:[[1604,1605,1581],256],64905:[[1605,1581,1580],256],64906:[[1605,1581,1605],256],64907:[[1605,1581,1610],256],64908:[[1605,1580,1581],256],64909:[[1605,1580,1605],256],64910:[[1605,1582,1580],256],64911:[[1605,1582,1605],256],64914:[[1605,1580,1582],256],64915:[[1607,1605,1580],256],64916:[[1607,1605,1605],256],64917:[[1606,1581,1605],256],64918:[[1606,1581,1609],256],64919:[[1606,1580,1605],256],64920:[[1606,1580,1605],256],64921:[[1606,1580,1609],256],64922:[[1606,1605,1610],256],64923:[[1606,1605,1609],256],64924:[[1610,1605,1605],256],64925:[[1610,1605,1605],256],64926:[[1576,1582,1610],256],64927:[[1578,1580,1610],256],64928:[[1578,1580,1609],256],64929:[[1578,1582,1610],256],64930:[[1578,1582,1609],256],64931:[[1578,1605,1610],256],64932:[[1578,1605,1609],256],64933:[[1580,1605,1610],256],64934:[[1580,1581,1609],256],64935:[[1580,1605,1609],256],64936:[[1587,1582,1609],256],64937:[[1589,1581,1610],256],64938:[[1588,1581,1610],256],64939:[[1590,1581,1610],256],64940:[[1604,1580,1610],256],64941:[[1604,1605,1610],256],64942:[[1610,1581,1610],256],64943:[[1610,1580,1610],256],64944:[[1610,1605,1610],256],64945:[[1605,1605,1610],256],64946:[[1602,1605,1610],256],64947:[[1606,1581,1610],256],64948:[[1602,1605,1581],256],64949:[[1604,1581,1605],256],64950:[[1593,1605,1610],256],64951:[[1603,1605,1610],256],64952:[[1606,1580,1581],256],64953:[[1605,1582,1610],256],64954:[[1604,1580,1605],256],64955:[[1603,1605,1605],256],64956:[[1604,1580,1605],256],64957:[[1606,1580,1581],256],64958:[[1580,1581,1610],256],64959:[[1581,1580,1610],256],64960:[[1605,1580,1610],256],64961:[[1601,1605,1610],256],64962:[[1576,1581,1610],256],64963:[[1603,1605,1605],256],64964:[[1593,1580,1605],256],64965:[[1589,1605,1605],256],64966:[[1587,1582,1610],256],64967:[[1606,1580,1610],256],65008:[[1589,1604,1746],256],65009:[[1602,1604,1746],256],65010:[[1575,1604,1604,1607],256],65011:[[1575,1603,1576,1585],256],65012:[[1605,1581,1605,1583],256],65013:[[1589,1604,1593,1605],256],65014:[[1585,1587,1608,1604],256],65015:[[1593,1604,1610,1607],256],65016:[[1608,1587,1604,1605],256],65017:[[1589,1604,1609],256],65018:[[1589,1604,1609,32,1575,1604,1604,1607,32,1593,1604,1610,1607,32,1608,1587,1604,1605],256],65019:[[1580,1604,32,1580,1604,1575,1604,1607],256],65020:[[1585,1740,1575,1604],256]},
65024:{65040:[[44],256],65041:[[12289],256],65042:[[12290],256],65043:[[58],256],65044:[[59],256],65045:[[33],256],65046:[[63],256],65047:[[12310],256],65048:[[12311],256],65049:[[8230],256],65056:[,230],65057:[,230],65058:[,230],65059:[,230],65060:[,230],65061:[,230],65062:[,230],65063:[,220],65064:[,220],65065:[,220],65066:[,220],65067:[,220],65068:[,220],65069:[,220],65072:[[8229],256],65073:[[8212],256],65074:[[8211],256],65075:[[95],256],65076:[[95],256],65077:[[40],256],65078:[[41],256],65079:[[123],256],65080:[[125],256],65081:[[12308],256],65082:[[12309],256],65083:[[12304],256],65084:[[12305],256],65085:[[12298],256],65086:[[12299],256],65087:[[12296],256],65088:[[12297],256],65089:[[12300],256],65090:[[12301],256],65091:[[12302],256],65092:[[12303],256],65095:[[91],256],65096:[[93],256],65097:[[8254],256],65098:[[8254],256],65099:[[8254],256],65100:[[8254],256],65101:[[95],256],65102:[[95],256],65103:[[95],256],65104:[[44],256],65105:[[12289],256],65106:[[46],256],65108:[[59],256],65109:[[58],256],65110:[[63],256],65111:[[33],256],65112:[[8212],256],65113:[[40],256],65114:[[41],256],65115:[[123],256],65116:[[125],256],65117:[[12308],256],65118:[[12309],256],65119:[[35],256],65120:[[38],256],65121:[[42],256],65122:[[43],256],65123:[[45],256],65124:[[60],256],65125:[[62],256],65126:[[61],256],65128:[[92],256],65129:[[36],256],65130:[[37],256],65131:[[64],256],65136:[[32,1611],256],65137:[[1600,1611],256],65138:[[32,1612],256],65140:[[32,1613],256],65142:[[32,1614],256],65143:[[1600,1614],256],65144:[[32,1615],256],65145:[[1600,1615],256],65146:[[32,1616],256],65147:[[1600,1616],256],65148:[[32,1617],256],65149:[[1600,1617],256],65150:[[32,1618],256],65151:[[1600,1618],256],65152:[[1569],256],65153:[[1570],256],65154:[[1570],256],65155:[[1571],256],65156:[[1571],256],65157:[[1572],256],65158:[[1572],256],65159:[[1573],256],65160:[[1573],256],65161:[[1574],256],65162:[[1574],256],65163:[[1574],256],65164:[[1574],256],65165:[[1575],256],65166:[[1575],256],65167:[[1576],256],65168:[[1576],256],65169:[[1576],256],65170:[[1576],256],65171:[[1577],256],65172:[[1577],256],65173:[[1578],256],65174:[[1578],256],65175:[[1578],256],65176:[[1578],256],65177:[[1579],256],65178:[[1579],256],65179:[[1579],256],65180:[[1579],256],65181:[[1580],256],65182:[[1580],256],65183:[[1580],256],65184:[[1580],256],65185:[[1581],256],65186:[[1581],256],65187:[[1581],256],65188:[[1581],256],65189:[[1582],256],65190:[[1582],256],65191:[[1582],256],65192:[[1582],256],65193:[[1583],256],65194:[[1583],256],65195:[[1584],256],65196:[[1584],256],65197:[[1585],256],65198:[[1585],256],65199:[[1586],256],65200:[[1586],256],65201:[[1587],256],65202:[[1587],256],65203:[[1587],256],65204:[[1587],256],65205:[[1588],256],65206:[[1588],256],65207:[[1588],256],65208:[[1588],256],65209:[[1589],256],65210:[[1589],256],65211:[[1589],256],65212:[[1589],256],65213:[[1590],256],65214:[[1590],256],65215:[[1590],256],65216:[[1590],256],65217:[[1591],256],65218:[[1591],256],65219:[[1591],256],65220:[[1591],256],65221:[[1592],256],65222:[[1592],256],65223:[[1592],256],65224:[[1592],256],65225:[[1593],256],65226:[[1593],256],65227:[[1593],256],65228:[[1593],256],65229:[[1594],256],65230:[[1594],256],65231:[[1594],256],65232:[[1594],256],65233:[[1601],256],65234:[[1601],256],65235:[[1601],256],65236:[[1601],256],65237:[[1602],256],65238:[[1602],256],65239:[[1602],256],65240:[[1602],256],65241:[[1603],256],65242:[[1603],256],65243:[[1603],256],65244:[[1603],256],65245:[[1604],256],65246:[[1604],256],65247:[[1604],256],65248:[[1604],256],65249:[[1605],256],65250:[[1605],256],65251:[[1605],256],65252:[[1605],256],65253:[[1606],256],65254:[[1606],256],65255:[[1606],256],65256:[[1606],256],65257:[[1607],256],65258:[[1607],256],65259:[[1607],256],65260:[[1607],256],65261:[[1608],256],65262:[[1608],256],65263:[[1609],256],65264:[[1609],256],65265:[[1610],256],65266:[[1610],256],65267:[[1610],256],65268:[[1610],256],65269:[[1604,1570],256],65270:[[1604,1570],256],65271:[[1604,1571],256],65272:[[1604,1571],256],65273:[[1604,1573],256],65274:[[1604,1573],256],65275:[[1604,1575],256],65276:[[1604,1575],256]},
65280:{65281:[[33],256],65282:[[34],256],65283:[[35],256],65284:[[36],256],65285:[[37],256],65286:[[38],256],65287:[[39],256],65288:[[40],256],65289:[[41],256],65290:[[42],256],65291:[[43],256],65292:[[44],256],65293:[[45],256],65294:[[46],256],65295:[[47],256],65296:[[48],256],65297:[[49],256],65298:[[50],256],65299:[[51],256],65300:[[52],256],65301:[[53],256],65302:[[54],256],65303:[[55],256],65304:[[56],256],65305:[[57],256],65306:[[58],256],65307:[[59],256],65308:[[60],256],65309:[[61],256],65310:[[62],256],65311:[[63],256],65312:[[64],256],65313:[[65],256],65314:[[66],256],65315:[[67],256],65316:[[68],256],65317:[[69],256],65318:[[70],256],65319:[[71],256],65320:[[72],256],65321:[[73],256],65322:[[74],256],65323:[[75],256],65324:[[76],256],65325:[[77],256],65326:[[78],256],65327:[[79],256],65328:[[80],256],65329:[[81],256],65330:[[82],256],65331:[[83],256],65332:[[84],256],65333:[[85],256],65334:[[86],256],65335:[[87],256],65336:[[88],256],65337:[[89],256],65338:[[90],256],65339:[[91],256],65340:[[92],256],65341:[[93],256],65342:[[94],256],65343:[[95],256],65344:[[96],256],65345:[[97],256],65346:[[98],256],65347:[[99],256],65348:[[100],256],65349:[[101],256],65350:[[102],256],65351:[[103],256],65352:[[104],256],65353:[[105],256],65354:[[106],256],65355:[[107],256],65356:[[108],256],65357:[[109],256],65358:[[110],256],65359:[[111],256],65360:[[112],256],65361:[[113],256],65362:[[114],256],65363:[[115],256],65364:[[116],256],65365:[[117],256],65366:[[118],256],65367:[[119],256],65368:[[120],256],65369:[[121],256],65370:[[122],256],65371:[[123],256],65372:[[124],256],65373:[[125],256],65374:[[126],256],65375:[[10629],256],65376:[[10630],256],65377:[[12290],256],65378:[[12300],256],65379:[[12301],256],65380:[[12289],256],65381:[[12539],256],65382:[[12530],256],65383:[[12449],256],65384:[[12451],256],65385:[[12453],256],65386:[[12455],256],65387:[[12457],256],65388:[[12515],256],65389:[[12517],256],65390:[[12519],256],65391:[[12483],256],65392:[[12540],256],65393:[[12450],256],65394:[[12452],256],65395:[[12454],256],65396:[[12456],256],65397:[[12458],256],65398:[[12459],256],65399:[[12461],256],65400:[[12463],256],65401:[[12465],256],65402:[[12467],256],65403:[[12469],256],65404:[[12471],256],65405:[[12473],256],65406:[[12475],256],65407:[[12477],256],65408:[[12479],256],65409:[[12481],256],65410:[[12484],256],65411:[[12486],256],65412:[[12488],256],65413:[[12490],256],65414:[[12491],256],65415:[[12492],256],65416:[[12493],256],65417:[[12494],256],65418:[[12495],256],65419:[[12498],256],65420:[[12501],256],65421:[[12504],256],65422:[[12507],256],65423:[[12510],256],65424:[[12511],256],65425:[[12512],256],65426:[[12513],256],65427:[[12514],256],65428:[[12516],256],65429:[[12518],256],65430:[[12520],256],65431:[[12521],256],65432:[[12522],256],65433:[[12523],256],65434:[[12524],256],65435:[[12525],256],65436:[[12527],256],65437:[[12531],256],65438:[[12441],256],65439:[[12442],256],65440:[[12644],256],65441:[[12593],256],65442:[[12594],256],65443:[[12595],256],65444:[[12596],256],65445:[[12597],256],65446:[[12598],256],65447:[[12599],256],65448:[[12600],256],65449:[[12601],256],65450:[[12602],256],65451:[[12603],256],65452:[[12604],256],65453:[[12605],256],65454:[[12606],256],65455:[[12607],256],65456:[[12608],256],65457:[[12609],256],65458:[[12610],256],65459:[[12611],256],65460:[[12612],256],65461:[[12613],256],65462:[[12614],256],65463:[[12615],256],65464:[[12616],256],65465:[[12617],256],65466:[[12618],256],65467:[[12619],256],65468:[[12620],256],65469:[[12621],256],65470:[[12622],256],65474:[[12623],256],65475:[[12624],256],65476:[[12625],256],65477:[[12626],256],65478:[[12627],256],65479:[[12628],256],65482:[[12629],256],65483:[[12630],256],65484:[[12631],256],65485:[[12632],256],65486:[[12633],256],65487:[[12634],256],65490:[[12635],256],65491:[[12636],256],65492:[[12637],256],65493:[[12638],256],65494:[[12639],256],65495:[[12640],256],65498:[[12641],256],65499:[[12642],256],65500:[[12643],256],65504:[[162],256],65505:[[163],256],65506:[[172],256],65507:[[175],256],65508:[[166],256],65509:[[165],256],65510:[[8361],256],65512:[[9474],256],65513:[[8592],256],65514:[[8593],256],65515:[[8594],256],65516:[[8595],256],65517:[[9632],256],65518:[[9675],256]}

};

   /***** Module to export */
   var unorm = {
      nfc: nfc,
      nfd: nfd,
      nfkc: nfkc,
      nfkd: nfkd
   };

   /*globals module:true,define:true*/

   // CommonJS
   if (typeof module === "object") {
      module.exports = unorm;

   // AMD
   } else if (typeof define === "function" && define.amd) {
      define("unorm", function () {
         return unorm;
      });

   // Global
   } else {
      root.unorm = unorm;
   }

   /***** Export as shim for String::normalize method *****/
   /*
      http://wiki.ecmascript.org/doku.php?id=harmony:specification_drafts#november_8_2013_draft_rev_21

      21.1.3.12 String.prototype.normalize(form="NFC")
      When the normalize method is called with one argument form, the following steps are taken:

      1. Let O be CheckObjectCoercible(this value).
      2. Let S be ToString(O).
      3. ReturnIfAbrupt(S).
      4. If form is not provided or undefined let form be "NFC".
      5. Let f be ToString(form).
      6. ReturnIfAbrupt(f).
      7. If f is not one of "NFC", "NFD", "NFKC", or "NFKD", then throw a RangeError Exception.
      8. Let ns be the String value is the result of normalizing S into the normalization form named by f as specified in Unicode Standard Annex #15, UnicodeNormalizatoin Forms.
      9. Return ns.

      The length property of the normalize method is 0.

      *NOTE* The normalize function is intentionally generic; it does not require that its this value be a String object. Therefore it can be transferred to other kinds of objects for use as a method.
   */
    unorm.shimApplied = false;

   if (!String.prototype.normalize) {
      Object.defineProperty(String.prototype, "normalize", {
         enumerable: false,
         configurable: true,
         writable: true,
         value: function normalize (/*form*/) {
            
            var str = "" + this;
            var form = arguments[0] === undefined ? "NFC" : arguments[0];

            if (this === null || this === undefined) {
               throw new TypeError("Cannot call method on " + Object.prototype.toString.call(this));
            }

            if (form === "NFC") {
               return unorm.nfc(str);
            } else if (form === "NFD") {
               return unorm.nfd(str);
            } else if (form === "NFKC") {
               return unorm.nfkc(str);
            } else if (form === "NFKD") {
               return unorm.nfkd(str);
            } else {
               throw new RangeError("Invalid normalization form: " + form);
            }
         }
      });

      unorm.shimApplied = true;
   }
}(this));

// svg-pan-zoom v3.5.0
// https://github.com/ariutta/svg-pan-zoom
(function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
var svgPanZoom = require('./svg-pan-zoom.js');

// UMD module definition
(function(window, document){
  // AMD
  if (typeof define === 'function' && define.amd) {
    define('svg-pan-zoom', function () {
      return svgPanZoom;
    });
  // CMD
  } else if (typeof module !== 'undefined' && module.exports) {
    module.exports = svgPanZoom;

    // Browser
    // Keep exporting globally as module.exports is available because of browserify
    window.svgPanZoom = svgPanZoom;
  }
})(window, document)

},{"./svg-pan-zoom.js":4}],2:[function(require,module,exports){
var SvgUtils = require('./svg-utilities');

module.exports = {
  enable: function(instance) {
    // Select (and create if necessary) defs
    var defs = instance.svg.querySelector('defs')
    if (!defs) {
      defs = document.createElementNS(SvgUtils.svgNS, 'defs')
      instance.svg.appendChild(defs)
    }

    // Check for style element, and create it if it doesn't exist
    var styleEl = defs.querySelector('style#svg-pan-zoom-controls-styles');
    if (!styleEl) {
      var style = document.createElementNS(SvgUtils.svgNS, 'style')
      style.setAttribute('id', 'svg-pan-zoom-controls-styles')
      style.setAttribute('type', 'text/css')
      style.textContent = '.svg-pan-zoom-control { cursor: pointer; fill: black; fill-opacity: 0.333; } .svg-pan-zoom-control:hover { fill-opacity: 0.8; } .svg-pan-zoom-control-background { fill: white; fill-opacity: 0.5; } .svg-pan-zoom-control-background { fill-opacity: 0.8; }'
      defs.appendChild(style)
    }

    // Zoom Group
    var zoomGroup = document.createElementNS(SvgUtils.svgNS, 'g');
    zoomGroup.setAttribute('id', 'svg-pan-zoom-controls');
    zoomGroup.setAttribute('transform', 'translate(' + ( instance.width - 70 ) + ' ' + ( instance.height - 76 ) + ') scale(0.75)');
    zoomGroup.setAttribute('class', 'svg-pan-zoom-control');

    // Control elements
    zoomGroup.appendChild(this._createZoomIn(instance))
    zoomGroup.appendChild(this._createZoomReset(instance))
    zoomGroup.appendChild(this._createZoomOut(instance))

    // Finally append created element
    instance.svg.appendChild(zoomGroup)

    // Cache control instance
    instance.controlIcons = zoomGroup
  }

, _createZoomIn: function(instance) {
    var zoomIn = document.createElementNS(SvgUtils.svgNS, 'g');
    zoomIn.setAttribute('id', 'svg-pan-zoom-zoom-in');
    zoomIn.setAttribute('transform', 'translate(30.5 5) scale(0.015)');
    zoomIn.setAttribute('class', 'svg-pan-zoom-control');
    zoomIn.addEventListener('click', function() {instance.getPublicInstance().zoomIn()}, false)
    zoomIn.addEventListener('touchstart', function() {instance.getPublicInstance().zoomIn()}, false)

    var zoomInBackground = document.createElementNS(SvgUtils.svgNS, 'rect'); // TODO change these background space fillers to rounded rectangles so they look prettier
    zoomInBackground.setAttribute('x', '0');
    zoomInBackground.setAttribute('y', '0');
    zoomInBackground.setAttribute('width', '1500'); // larger than expected because the whole group is transformed to scale down
    zoomInBackground.setAttribute('height', '1400');
    zoomInBackground.setAttribute('class', 'svg-pan-zoom-control-background');
    zoomIn.appendChild(zoomInBackground);

    var zoomInShape = document.createElementNS(SvgUtils.svgNS, 'path');
    zoomInShape.setAttribute('d', 'M1280 576v128q0 26 -19 45t-45 19h-320v320q0 26 -19 45t-45 19h-128q-26 0 -45 -19t-19 -45v-320h-320q-26 0 -45 -19t-19 -45v-128q0 -26 19 -45t45 -19h320v-320q0 -26 19 -45t45 -19h128q26 0 45 19t19 45v320h320q26 0 45 19t19 45zM1536 1120v-960 q0 -119 -84.5 -203.5t-203.5 -84.5h-960q-119 0 -203.5 84.5t-84.5 203.5v960q0 119 84.5 203.5t203.5 84.5h960q119 0 203.5 -84.5t84.5 -203.5z');
    zoomInShape.setAttribute('class', 'svg-pan-zoom-control-element');
    zoomIn.appendChild(zoomInShape);

    return zoomIn
  }

, _createZoomReset: function(instance){
    // reset
    var resetPanZoomControl = document.createElementNS(SvgUtils.svgNS, 'g');
    resetPanZoomControl.setAttribute('id', 'svg-pan-zoom-reset-pan-zoom');
    resetPanZoomControl.setAttribute('transform', 'translate(5 35) scale(0.4)');
    resetPanZoomControl.setAttribute('class', 'svg-pan-zoom-control');
    resetPanZoomControl.addEventListener('click', function() {instance.getPublicInstance().reset()}, false);
    resetPanZoomControl.addEventListener('touchstart', function() {instance.getPublicInstance().reset()}, false);

    var resetPanZoomControlBackground = document.createElementNS(SvgUtils.svgNS, 'rect'); // TODO change these background space fillers to rounded rectangles so they look prettier
    resetPanZoomControlBackground.setAttribute('x', '2');
    resetPanZoomControlBackground.setAttribute('y', '2');
    resetPanZoomControlBackground.setAttribute('width', '182'); // larger than expected because the whole group is transformed to scale down
    resetPanZoomControlBackground.setAttribute('height', '58');
    resetPanZoomControlBackground.setAttribute('class', 'svg-pan-zoom-control-background');
    resetPanZoomControl.appendChild(resetPanZoomControlBackground);

    var resetPanZoomControlShape1 = document.createElementNS(SvgUtils.svgNS, 'path');
    resetPanZoomControlShape1.setAttribute('d', 'M33.051,20.632c-0.742-0.406-1.854-0.609-3.338-0.609h-7.969v9.281h7.769c1.543,0,2.701-0.188,3.473-0.562c1.365-0.656,2.048-1.953,2.048-3.891C35.032,22.757,34.372,21.351,33.051,20.632z');
    resetPanZoomControlShape1.setAttribute('class', 'svg-pan-zoom-control-element');
    resetPanZoomControl.appendChild(resetPanZoomControlShape1);

    var resetPanZoomControlShape2 = document.createElementNS(SvgUtils.svgNS, 'path');
    resetPanZoomControlShape2.setAttribute('d', 'M170.231,0.5H15.847C7.102,0.5,0.5,5.708,0.5,11.84v38.861C0.5,56.833,7.102,61.5,15.847,61.5h154.384c8.745,0,15.269-4.667,15.269-10.798V11.84C185.5,5.708,178.976,0.5,170.231,0.5z M42.837,48.569h-7.969c-0.219-0.766-0.375-1.383-0.469-1.852c-0.188-0.969-0.289-1.961-0.305-2.977l-0.047-3.211c-0.03-2.203-0.41-3.672-1.142-4.406c-0.732-0.734-2.103-1.102-4.113-1.102h-7.05v13.547h-7.055V14.022h16.524c2.361,0.047,4.178,0.344,5.45,0.891c1.272,0.547,2.351,1.352,3.234,2.414c0.731,0.875,1.31,1.844,1.737,2.906s0.64,2.273,0.64,3.633c0,1.641-0.414,3.254-1.242,4.84s-2.195,2.707-4.102,3.363c1.594,0.641,2.723,1.551,3.387,2.73s0.996,2.98,0.996,5.402v2.32c0,1.578,0.063,2.648,0.19,3.211c0.19,0.891,0.635,1.547,1.333,1.969V48.569z M75.579,48.569h-26.18V14.022h25.336v6.117H56.454v7.336h16.781v6H56.454v8.883h19.125V48.569z M104.497,46.331c-2.44,2.086-5.887,3.129-10.34,3.129c-4.548,0-8.125-1.027-10.731-3.082s-3.909-4.879-3.909-8.473h6.891c0.224,1.578,0.662,2.758,1.316,3.539c1.196,1.422,3.246,2.133,6.15,2.133c1.739,0,3.151-0.188,4.236-0.562c2.058-0.719,3.087-2.055,3.087-4.008c0-1.141-0.504-2.023-1.512-2.648c-1.008-0.609-2.607-1.148-4.796-1.617l-3.74-0.82c-3.676-0.812-6.201-1.695-7.576-2.648c-2.328-1.594-3.492-4.086-3.492-7.477c0-3.094,1.139-5.664,3.417-7.711s5.623-3.07,10.036-3.07c3.685,0,6.829,0.965,9.431,2.895c2.602,1.93,3.966,4.73,4.093,8.402h-6.938c-0.128-2.078-1.057-3.555-2.787-4.43c-1.154-0.578-2.587-0.867-4.301-0.867c-1.907,0-3.428,0.375-4.565,1.125c-1.138,0.75-1.706,1.797-1.706,3.141c0,1.234,0.561,2.156,1.682,2.766c0.721,0.406,2.25,0.883,4.589,1.43l6.063,1.43c2.657,0.625,4.648,1.461,5.975,2.508c2.059,1.625,3.089,3.977,3.089,7.055C108.157,41.624,106.937,44.245,104.497,46.331z M139.61,48.569h-26.18V14.022h25.336v6.117h-18.281v7.336h16.781v6h-16.781v8.883h19.125V48.569z M170.337,20.14h-10.336v28.43h-7.266V20.14h-10.383v-6.117h27.984V20.14z');
    resetPanZoomControlShape2.setAttribute('class', 'svg-pan-zoom-control-element');
    resetPanZoomControl.appendChild(resetPanZoomControlShape2);

    return resetPanZoomControl
  }

, _createZoomOut: function(instance){
    // zoom out
    var zoomOut = document.createElementNS(SvgUtils.svgNS, 'g');
    zoomOut.setAttribute('id', 'svg-pan-zoom-zoom-out');
    zoomOut.setAttribute('transform', 'translate(30.5 70) scale(0.015)');
    zoomOut.setAttribute('class', 'svg-pan-zoom-control');
    zoomOut.addEventListener('click', function() {instance.getPublicInstance().zoomOut()}, false);
    zoomOut.addEventListener('touchstart', function() {instance.getPublicInstance().zoomOut()}, false);

    var zoomOutBackground = document.createElementNS(SvgUtils.svgNS, 'rect'); // TODO change these background space fillers to rounded rectangles so they look prettier
    zoomOutBackground.setAttribute('x', '0');
    zoomOutBackground.setAttribute('y', '0');
    zoomOutBackground.setAttribute('width', '1500'); // larger than expected because the whole group is transformed to scale down
    zoomOutBackground.setAttribute('height', '1400');
    zoomOutBackground.setAttribute('class', 'svg-pan-zoom-control-background');
    zoomOut.appendChild(zoomOutBackground);

    var zoomOutShape = document.createElementNS(SvgUtils.svgNS, 'path');
    zoomOutShape.setAttribute('d', 'M1280 576v128q0 26 -19 45t-45 19h-896q-26 0 -45 -19t-19 -45v-128q0 -26 19 -45t45 -19h896q26 0 45 19t19 45zM1536 1120v-960q0 -119 -84.5 -203.5t-203.5 -84.5h-960q-119 0 -203.5 84.5t-84.5 203.5v960q0 119 84.5 203.5t203.5 84.5h960q119 0 203.5 -84.5 t84.5 -203.5z');
    zoomOutShape.setAttribute('class', 'svg-pan-zoom-control-element');
    zoomOut.appendChild(zoomOutShape);

    return zoomOut
  }

, disable: function(instance) {
    if (instance.controlIcons) {
      instance.controlIcons.parentNode.removeChild(instance.controlIcons)
      instance.controlIcons = null
    }
  }
}

},{"./svg-utilities":5}],3:[function(require,module,exports){
var SvgUtils = require('./svg-utilities')
  , Utils = require('./utilities')
  ;

var ShadowViewport = function(viewport, options){
  this.init(viewport, options)
}

/**
 * Initialization
 *
 * @param  {SVGElement} viewport
 * @param  {Object} options
 */
ShadowViewport.prototype.init = function(viewport, options) {
  // DOM Elements
  this.viewport = viewport
  this.options = options

  // State cache
  this.originalState = {zoom: 1, x: 0, y: 0}
  this.activeState = {zoom: 1, x: 0, y: 0}

  this.updateCTMCached = Utils.proxy(this.updateCTM, this)

  // Create a custom requestAnimationFrame taking in account refreshRate
  this.requestAnimationFrame = Utils.createRequestAnimationFrame(this.options.refreshRate)

  // ViewBox
  this.viewBox = {x: 0, y: 0, width: 0, height: 0}
  this.cacheViewBox()

  // Process CTM
  var newCTM = this.processCTM()

  // Update viewport CTM and cache zoom and pan
  this.setCTM(newCTM)

  // Update CTM in this frame
  this.updateCTM()
}

/**
 * Cache initial viewBox value
 * If no viewBox is defined, then use viewport size/position instead for viewBox values
 */
ShadowViewport.prototype.cacheViewBox = function() {
  var svgViewBox = this.options.svg.getAttribute('viewBox')

  if (svgViewBox) {
    var viewBoxValues = svgViewBox.split(/[\s\,]/).filter(function(v){return v}).map(parseFloat)

    // Cache viewbox x and y offset
    this.viewBox.x = viewBoxValues[0]
    this.viewBox.y = viewBoxValues[1]
    this.viewBox.width = viewBoxValues[2]
    this.viewBox.height = viewBoxValues[3]

    var zoom = Math.min(this.options.width / this.viewBox.width, this.options.height / this.viewBox.height)

    // Update active state
    this.activeState.zoom = zoom
    this.activeState.x = (this.options.width - this.viewBox.width * zoom) / 2
    this.activeState.y = (this.options.height - this.viewBox.height * zoom) / 2

    // Force updating CTM
    this.updateCTMOnNextFrame()

    this.options.svg.removeAttribute('viewBox')
  } else {
    this.simpleViewBoxCache()
  }
}

/**
 * Recalculate viewport sizes and update viewBox cache
 */
ShadowViewport.prototype.simpleViewBoxCache = function() {
  var bBox = this.viewport.getBBox()

  this.viewBox.x = bBox.x
  this.viewBox.y = bBox.y
  this.viewBox.width = bBox.width
  this.viewBox.height = bBox.height
}

/**
 * Returns a viewbox object. Safe to alter
 *
 * @return {Object} viewbox object
 */
ShadowViewport.prototype.getViewBox = function() {
  return Utils.extend({}, this.viewBox)
}

/**
 * Get initial zoom and pan values. Save them into originalState
 * Parses viewBox attribute to alter initial sizes
 *
 * @return {CTM} CTM object based on options
 */
ShadowViewport.prototype.processCTM = function() {
  var newCTM = this.getCTM()

  if (this.options.fit || this.options.contain) {
    var newScale;
    if (this.options.fit) {
      newScale = Math.min(this.options.width/this.viewBox.width, this.options.height/this.viewBox.height);
    } else {
      newScale = Math.max(this.options.width/this.viewBox.width, this.options.height/this.viewBox.height);
    }

    newCTM.a = newScale; //x-scale
    newCTM.d = newScale; //y-scale
    newCTM.e = -this.viewBox.x * newScale; //x-transform
    newCTM.f = -this.viewBox.y * newScale; //y-transform
  }

  if (this.options.center) {
    var offsetX = (this.options.width - (this.viewBox.width + this.viewBox.x * 2) * newCTM.a) * 0.5
      , offsetY = (this.options.height - (this.viewBox.height + this.viewBox.y * 2) * newCTM.a) * 0.5

    newCTM.e = offsetX
    newCTM.f = offsetY
  }

  // Cache initial values. Based on activeState and fix+center opitons
  this.originalState.zoom = newCTM.a
  this.originalState.x = newCTM.e
  this.originalState.y = newCTM.f

  return newCTM
}

/**
 * Return originalState object. Safe to alter
 *
 * @return {Object}
 */
ShadowViewport.prototype.getOriginalState = function() {
  return Utils.extend({}, this.originalState)
}

/**
 * Return actualState object. Safe to alter
 *
 * @return {Object}
 */
ShadowViewport.prototype.getState = function() {
  return Utils.extend({}, this.activeState)
}

/**
 * Get zoom scale
 *
 * @return {Float} zoom scale
 */
ShadowViewport.prototype.getZoom = function() {
  return this.activeState.zoom
}

/**
 * Get zoom scale for pubilc usage
 *
 * @return {Float} zoom scale
 */
ShadowViewport.prototype.getRelativeZoom = function() {
  return this.activeState.zoom / this.originalState.zoom
}

/**
 * Compute zoom scale for pubilc usage
 *
 * @return {Float} zoom scale
 */
ShadowViewport.prototype.computeRelativeZoom = function(scale) {
  return scale / this.originalState.zoom
}

/**
 * Get pan
 *
 * @return {Object}
 */
ShadowViewport.prototype.getPan = function() {
  return {x: this.activeState.x, y: this.activeState.y}
}

/**
 * Return cached viewport CTM value that can be safely modified
 *
 * @return {SVGMatrix}
 */
ShadowViewport.prototype.getCTM = function() {
  var safeCTM = this.options.svg.createSVGMatrix()

  // Copy values manually as in FF they are not itterable
  safeCTM.a = this.activeState.zoom
  safeCTM.b = 0
  safeCTM.c = 0
  safeCTM.d = this.activeState.zoom
  safeCTM.e = this.activeState.x
  safeCTM.f = this.activeState.y

  return safeCTM
}

/**
 * Set a new CTM
 *
 * @param {SVGMatrix} newCTM
 */
ShadowViewport.prototype.setCTM = function(newCTM) {
  var willZoom = this.isZoomDifferent(newCTM)
    , willPan = this.isPanDifferent(newCTM)

  if (willZoom || willPan) {
    // Before zoom
    if (willZoom) {
      // If returns false then cancel zooming
      if (this.options.beforeZoom(this.getRelativeZoom(), this.computeRelativeZoom(newCTM.a)) === false) {
        newCTM.a = newCTM.d = this.activeState.zoom
        willZoom = false
      } else {
        this.updateCache(newCTM);
        this.options.onZoom(this.getRelativeZoom())
      }
    }

    // Before pan
    if (willPan) {
      var preventPan = this.options.beforePan(this.getPan(), {x: newCTM.e, y: newCTM.f})
          // If prevent pan is an object
        , preventPanX = false
        , preventPanY = false

      // If prevent pan is Boolean false
      if (preventPan === false) {
        // Set x and y same as before
        newCTM.e = this.getPan().x
        newCTM.f = this.getPan().y

        preventPanX = preventPanY = true
      } else if (Utils.isObject(preventPan)) {
        // Check for X axes attribute
        if (preventPan.x === false) {
          // Prevent panning on x axes
          newCTM.e = this.getPan().x
          preventPanX = true
        } else if (Utils.isNumber(preventPan.x)) {
          // Set a custom pan value
          newCTM.e = preventPan.x
        }

        // Check for Y axes attribute
        if (preventPan.y === false) {
          // Prevent panning on x axes
          newCTM.f = this.getPan().y
          preventPanY = true
        } else if (Utils.isNumber(preventPan.y)) {
          // Set a custom pan value
          newCTM.f = preventPan.y
        }
      }

      // Update willPan flag
      if (preventPanX && preventPanY) {
        willPan = false
      } else {
        this.updateCache(newCTM);
        this.options.onPan(this.getPan());
      }
    }

    // Check again if should zoom or pan
    if (willZoom || willPan) {
      this.updateCTMOnNextFrame()
    }
  }
}

ShadowViewport.prototype.isZoomDifferent = function(newCTM) {
  return this.activeState.zoom !== newCTM.a
}

ShadowViewport.prototype.isPanDifferent = function(newCTM) {
  return this.activeState.x !== newCTM.e || this.activeState.y !== newCTM.f
}


/**
 * Update cached CTM and active state
 *
 * @param {SVGMatrix} newCTM
 */
ShadowViewport.prototype.updateCache = function(newCTM) {
  this.activeState.zoom = newCTM.a
  this.activeState.x = newCTM.e
  this.activeState.y = newCTM.f
}

ShadowViewport.prototype.pendingUpdate = false

/**
 * Place a request to update CTM on next Frame
 */
ShadowViewport.prototype.updateCTMOnNextFrame = function() {
  if (!this.pendingUpdate) {
    // Lock
    this.pendingUpdate = true

    // Throttle next update
    this.requestAnimationFrame.call(window, this.updateCTMCached)
  }
}

/**
 * Update viewport CTM with cached CTM
 */
ShadowViewport.prototype.updateCTM = function() {
  var ctm = this.getCTM()

  // Updates SVG element
  SvgUtils.setCTM(this.viewport, ctm, this.defs)

  // Free the lock
  this.pendingUpdate = false

  // Notify about the update
  if(this.options.onUpdatedCTM) {
    this.options.onUpdatedCTM(ctm)
  }
}

module.exports = function(viewport, options){
  return new ShadowViewport(viewport, options)
}

},{"./svg-utilities":5,"./utilities":7}],4:[function(require,module,exports){
var Wheel = require('./uniwheel')
, ControlIcons = require('./control-icons')
, Utils = require('./utilities')
, SvgUtils = require('./svg-utilities')
, ShadowViewport = require('./shadow-viewport')

var SvgPanZoom = function(svg, options) {
  this.init(svg, options)
}

var optionsDefaults = {
  viewportSelector: '.svg-pan-zoom_viewport' // Viewport selector. Can be querySelector string or SVGElement
, panEnabled: true // enable or disable panning (default enabled)
, controlIconsEnabled: false // insert icons to give user an option in addition to mouse events to control pan/zoom (default disabled)
, zoomEnabled: true // enable or disable zooming (default enabled)
, dblClickZoomEnabled: true // enable or disable zooming by double clicking (default enabled)
, mouseWheelZoomEnabled: true // enable or disable zooming by mouse wheel (default enabled)
, preventMouseEventsDefault: true // enable or disable preventDefault for mouse events
, zoomScaleSensitivity: 0.1 // Zoom sensitivity
, minZoom: 0.5 // Minimum Zoom level
, maxZoom: 10 // Maximum Zoom level
, fit: true // enable or disable viewport fit in SVG (default true)
, contain: false // enable or disable viewport contain the svg (default false)
, center: true // enable or disable viewport centering in SVG (default true)
, refreshRate: 'auto' // Maximum number of frames per second (altering SVG's viewport)
, beforeZoom: null
, onZoom: null
, beforePan: null
, onPan: null
, customEventsHandler: null
, eventsListenerElement: null
, onUpdatedCTM: null
}

SvgPanZoom.prototype.init = function(svg, options) {
  var that = this

  this.svg = svg
  this.defs = svg.querySelector('defs')

  // Add default attributes to SVG
  SvgUtils.setupSvgAttributes(this.svg)

  // Set options
  this.options = Utils.extend(Utils.extend({}, optionsDefaults), options)

  // Set default state
  this.state = 'none'

  // Get dimensions
  var boundingClientRectNormalized = SvgUtils.getBoundingClientRectNormalized(svg)
  this.width = boundingClientRectNormalized.width
  this.height = boundingClientRectNormalized.height

  // Init shadow viewport
  this.viewport = ShadowViewport(SvgUtils.getOrCreateViewport(this.svg, this.options.viewportSelector), {
    svg: this.svg
  , width: this.width
  , height: this.height
  , fit: this.options.fit
  , contain: this.options.contain
  , center: this.options.center
  , refreshRate: this.options.refreshRate
  // Put callbacks into functions as they can change through time
  , beforeZoom: function(oldScale, newScale) {
      if (that.viewport && that.options.beforeZoom) {return that.options.beforeZoom(oldScale, newScale)}
    }
  , onZoom: function(scale) {
      if (that.viewport && that.options.onZoom) {return that.options.onZoom(scale)}
    }
  , beforePan: function(oldPoint, newPoint) {
      if (that.viewport && that.options.beforePan) {return that.options.beforePan(oldPoint, newPoint)}
    }
  , onPan: function(point) {
      if (that.viewport && that.options.onPan) {return that.options.onPan(point)}
    }
  , onUpdatedCTM: function(ctm) {
      if (that.viewport && that.options.onUpdatedCTM) {return that.options.onUpdatedCTM(ctm)}
    }
  })

  // Wrap callbacks into public API context
  var publicInstance = this.getPublicInstance()
  publicInstance.setBeforeZoom(this.options.beforeZoom)
  publicInstance.setOnZoom(this.options.onZoom)
  publicInstance.setBeforePan(this.options.beforePan)
  publicInstance.setOnPan(this.options.onPan)
  publicInstance.setOnUpdatedCTM(this.options.onUpdatedCTM)

  if (this.options.controlIconsEnabled) {
    ControlIcons.enable(this)
  }

  // Init events handlers
  this.lastMouseWheelEventTime = Date.now()
  this.setupHandlers()
}

/**
 * Register event handlers
 */
SvgPanZoom.prototype.setupHandlers = function() {
  var that = this
    , prevEvt = null // use for touchstart event to detect double tap
    ;

  this.eventListeners = {
    // Mouse down group
    mousedown: function(evt) {
      var result = that.handleMouseDown(evt, prevEvt);
      prevEvt = evt
      return result;
    }
  , touchstart: function(evt) {
      var result = that.handleMouseDown(evt, prevEvt);
      prevEvt = evt
      return result;
    }

    // Mouse up group
  , mouseup: function(evt) {
      return that.handleMouseUp(evt);
    }
  , touchend: function(evt) {
      return that.handleMouseUp(evt);
    }

    // Mouse move group
  , mousemove: function(evt) {
      return that.handleMouseMove(evt);
    }
  , touchmove: function(evt) {
      return that.handleMouseMove(evt);
    }

    // Mouse leave group
  , mouseleave: function(evt) {
      return that.handleMouseUp(evt);
    }
  , touchleave: function(evt) {
      return that.handleMouseUp(evt);
    }
  , touchcancel: function(evt) {
      return that.handleMouseUp(evt);
    }
  }

  // Init custom events handler if available
  if (this.options.customEventsHandler != null) { // jshint ignore:line
    this.options.customEventsHandler.init({
      svgElement: this.svg
    , eventsListenerElement: this.options.eventsListenerElement
    , instance: this.getPublicInstance()
    })

    // Custom event handler may halt builtin listeners
    var haltEventListeners = this.options.customEventsHandler.haltEventListeners
    if (haltEventListeners && haltEventListeners.length) {
      for (var i = haltEventListeners.length - 1; i >= 0; i--) {
        if (this.eventListeners.hasOwnProperty(haltEventListeners[i])) {
          delete this.eventListeners[haltEventListeners[i]]
        }
      }
    }
  }

  // Bind eventListeners
  for (var event in this.eventListeners) {
    // Attach event to eventsListenerElement or SVG if not available
    (this.options.eventsListenerElement || this.svg)
      .addEventListener(event, this.eventListeners[event], false)
  }

  // Zoom using mouse wheel
  if (this.options.mouseWheelZoomEnabled) {
    this.options.mouseWheelZoomEnabled = false // set to false as enable will set it back to true
    this.enableMouseWheelZoom()
  }
}

/**
 * Enable ability to zoom using mouse wheel
 */
SvgPanZoom.prototype.enableMouseWheelZoom = function() {
  if (!this.options.mouseWheelZoomEnabled) {
    var that = this

    // Mouse wheel listener
    this.wheelListener = function(evt) {
      return that.handleMouseWheel(evt);
    }

    // Bind wheelListener
    Wheel.on(this.options.eventsListenerElement || this.svg, this.wheelListener, false)

    this.options.mouseWheelZoomEnabled = true
  }
}

/**
 * Disable ability to zoom using mouse wheel
 */
SvgPanZoom.prototype.disableMouseWheelZoom = function() {
  if (this.options.mouseWheelZoomEnabled) {
    Wheel.off(this.options.eventsListenerElement || this.svg, this.wheelListener, false)
    this.options.mouseWheelZoomEnabled = false
  }
}

/**
 * Handle mouse wheel event
 *
 * @param  {Event} evt
 */
SvgPanZoom.prototype.handleMouseWheel = function(evt) {
  if (!this.options.zoomEnabled || this.state !== 'none') {
    return;
  }

  if (this.options.preventMouseEventsDefault){
    if (evt.preventDefault) {
      evt.preventDefault();
    } else {
      evt.returnValue = false;
    }
  }

  // Default delta in case that deltaY is not available
  var delta = evt.deltaY || 1
    , timeDelta = Date.now() - this.lastMouseWheelEventTime
    , divider = 3 + Math.max(0, 30 - timeDelta)

  // Update cache
  this.lastMouseWheelEventTime = Date.now()

  // Make empirical adjustments for browsers that give deltaY in pixels (deltaMode=0)
  if ('deltaMode' in evt && evt.deltaMode === 0 && evt.wheelDelta) {
    delta = evt.deltaY === 0 ? 0 :  Math.abs(evt.wheelDelta) / evt.deltaY
  }

  delta = -0.3 < delta && delta < 0.3 ? delta : (delta > 0 ? 1 : -1) * Math.log(Math.abs(delta) + 10) / divider

  var inversedScreenCTM = this.svg.getScreenCTM().inverse()
    , relativeMousePoint = SvgUtils.getEventPoint(evt, this.svg).matrixTransform(inversedScreenCTM)
    , zoom = Math.pow(1 + this.options.zoomScaleSensitivity, (-1) * delta); // multiplying by neg. 1 so as to make zoom in/out behavior match Google maps behavior

  this.zoomAtPoint(zoom, relativeMousePoint)
}

/**
 * Zoom in at a SVG point
 *
 * @param  {SVGPoint} point
 * @param  {Float} zoomScale    Number representing how much to zoom
 * @param  {Boolean} zoomAbsolute Default false. If true, zoomScale is treated as an absolute value.
 *                                Otherwise, zoomScale is treated as a multiplied (e.g. 1.10 would zoom in 10%)
 */
SvgPanZoom.prototype.zoomAtPoint = function(zoomScale, point, zoomAbsolute) {
  var originalState = this.viewport.getOriginalState()

  if (!zoomAbsolute) {
    // Fit zoomScale in set bounds
    if (this.getZoom() * zoomScale < this.options.minZoom * originalState.zoom) {
      zoomScale = (this.options.minZoom * originalState.zoom) / this.getZoom()
    } else if (this.getZoom() * zoomScale > this.options.maxZoom * originalState.zoom) {
      zoomScale = (this.options.maxZoom * originalState.zoom) / this.getZoom()
    }
  } else {
    // Fit zoomScale in set bounds
    zoomScale = Math.max(this.options.minZoom * originalState.zoom, Math.min(this.options.maxZoom * originalState.zoom, zoomScale))
    // Find relative scale to achieve desired scale
    zoomScale = zoomScale/this.getZoom()
  }

  var oldCTM = this.viewport.getCTM()
    , relativePoint = point.matrixTransform(oldCTM.inverse())
    , modifier = this.svg.createSVGMatrix().translate(relativePoint.x, relativePoint.y).scale(zoomScale).translate(-relativePoint.x, -relativePoint.y)
    , newCTM = oldCTM.multiply(modifier)

  if (newCTM.a !== oldCTM.a) {
    this.viewport.setCTM(newCTM)
  }
}

/**
 * Zoom at center point
 *
 * @param  {Float} scale
 * @param  {Boolean} absolute Marks zoom scale as relative or absolute
 */
SvgPanZoom.prototype.zoom = function(scale, absolute) {
  this.zoomAtPoint(scale, SvgUtils.getSvgCenterPoint(this.svg, this.width, this.height), absolute)
}

/**
 * Zoom used by public instance
 *
 * @param  {Float} scale
 * @param  {Boolean} absolute Marks zoom scale as relative or absolute
 */
SvgPanZoom.prototype.publicZoom = function(scale, absolute) {
  if (absolute) {
    scale = this.computeFromRelativeZoom(scale)
  }

  this.zoom(scale, absolute)
}

/**
 * Zoom at point used by public instance
 *
 * @param  {Float} scale
 * @param  {SVGPoint|Object} point    An object that has x and y attributes
 * @param  {Boolean} absolute Marks zoom scale as relative or absolute
 */
SvgPanZoom.prototype.publicZoomAtPoint = function(scale, point, absolute) {
  if (absolute) {
    // Transform zoom into a relative value
    scale = this.computeFromRelativeZoom(scale)
  }

  // If not a SVGPoint but has x and y then create a SVGPoint
  if (Utils.getType(point) !== 'SVGPoint') {
    if('x' in point && 'y' in point) {
      point = SvgUtils.createSVGPoint(this.svg, point.x, point.y)
    } else {
      throw new Error('Given point is invalid')
    }
  }

  this.zoomAtPoint(scale, point, absolute)
}

/**
 * Get zoom scale
 *
 * @return {Float} zoom scale
 */
SvgPanZoom.prototype.getZoom = function() {
  return this.viewport.getZoom()
}

/**
 * Get zoom scale for public usage
 *
 * @return {Float} zoom scale
 */
SvgPanZoom.prototype.getRelativeZoom = function() {
  return this.viewport.getRelativeZoom()
}

/**
 * Compute actual zoom from public zoom
 *
 * @param  {Float} zoom
 * @return {Float} zoom scale
 */
SvgPanZoom.prototype.computeFromRelativeZoom = function(zoom) {
  return zoom * this.viewport.getOriginalState().zoom
}

/**
 * Set zoom to initial state
 */
SvgPanZoom.prototype.resetZoom = function() {
  var originalState = this.viewport.getOriginalState()

  this.zoom(originalState.zoom, true);
}

/**
 * Set pan to initial state
 */
SvgPanZoom.prototype.resetPan = function() {
  this.pan(this.viewport.getOriginalState());
}

/**
 * Set pan and zoom to initial state
 */
SvgPanZoom.prototype.reset = function() {
  this.resetZoom()
  this.resetPan()
}

/**
 * Handle double click event
 * See handleMouseDown() for alternate detection method
 *
 * @param {Event} evt
 */
SvgPanZoom.prototype.handleDblClick = function(evt) {
  if (this.options.preventMouseEventsDefault) {
    if (evt.preventDefault) {
      evt.preventDefault()
    } else {
      evt.returnValue = false
    }
  }

  // Check if target was a control button
  if (this.options.controlIconsEnabled) {
    var targetClass = evt.target.getAttribute('class') || ''
    if (targetClass.indexOf('svg-pan-zoom-control') > -1) {
      return false
    }
  }

  var zoomFactor

  if (evt.shiftKey) {
    zoomFactor = 1/((1 + this.options.zoomScaleSensitivity) * 2) // zoom out when shift key pressed
  } else {
    zoomFactor = (1 + this.options.zoomScaleSensitivity) * 2
  }

  var point = SvgUtils.getEventPoint(evt, this.svg).matrixTransform(this.svg.getScreenCTM().inverse())
  this.zoomAtPoint(zoomFactor, point)
}

/**
 * Handle click event
 *
 * @param {Event} evt
 */
SvgPanZoom.prototype.handleMouseDown = function(evt, prevEvt) {
  if (this.options.preventMouseEventsDefault) {
    if (evt.preventDefault) {
      evt.preventDefault()
    } else {
      evt.returnValue = false
    }
  }

  Utils.mouseAndTouchNormalize(evt, this.svg)

  // Double click detection; more consistent than ondblclick
  if (this.options.dblClickZoomEnabled && Utils.isDblClick(evt, prevEvt)){
    this.handleDblClick(evt)
  } else {
    // Pan mode
    this.state = 'pan'
    this.firstEventCTM = this.viewport.getCTM()
    this.stateOrigin = SvgUtils.getEventPoint(evt, this.svg).matrixTransform(this.firstEventCTM.inverse())
  }
}

/**
 * Handle mouse move event
 *
 * @param  {Event} evt
 */
SvgPanZoom.prototype.handleMouseMove = function(evt) {
  if (this.options.preventMouseEventsDefault) {
    if (evt.preventDefault) {
      evt.preventDefault()
    } else {
      evt.returnValue = false
    }
  }

  if (this.state === 'pan' && this.options.panEnabled) {
    // Pan mode
    var point = SvgUtils.getEventPoint(evt, this.svg).matrixTransform(this.firstEventCTM.inverse())
      , viewportCTM = this.firstEventCTM.translate(point.x - this.stateOrigin.x, point.y - this.stateOrigin.y)

    this.viewport.setCTM(viewportCTM)
  }
}

/**
 * Handle mouse button release event
 *
 * @param {Event} evt
 */
SvgPanZoom.prototype.handleMouseUp = function(evt) {
  if (this.options.preventMouseEventsDefault) {
    if (evt.preventDefault) {
      evt.preventDefault()
    } else {
      evt.returnValue = false
    }
  }

  if (this.state === 'pan') {
    // Quit pan mode
    this.state = 'none'
  }
}

/**
 * Adjust viewport size (only) so it will fit in SVG
 * Does not center image
 */
SvgPanZoom.prototype.fit = function() {
  var viewBox = this.viewport.getViewBox()
    , newScale = Math.min(this.width/viewBox.width, this.height/viewBox.height)

  this.zoom(newScale, true)
}

/**
 * Adjust viewport size (only) so it will contain the SVG
 * Does not center image
 */
SvgPanZoom.prototype.contain = function() {
  var viewBox = this.viewport.getViewBox()
    , newScale = Math.max(this.width/viewBox.width, this.height/viewBox.height)

  this.zoom(newScale, true)
}

/**
 * Adjust viewport pan (only) so it will be centered in SVG
 * Does not zoom/fit/contain image
 */
SvgPanZoom.prototype.center = function() {
  var viewBox = this.viewport.getViewBox()
    , offsetX = (this.width - (viewBox.width + viewBox.x * 2) * this.getZoom()) * 0.5
    , offsetY = (this.height - (viewBox.height + viewBox.y * 2) * this.getZoom()) * 0.5

  this.getPublicInstance().pan({x: offsetX, y: offsetY})
}

/**
 * Update content cached BorderBox
 * Use when viewport contents change
 */
SvgPanZoom.prototype.updateBBox = function() {
  this.viewport.simpleViewBoxCache()
}

/**
 * Pan to a rendered position
 *
 * @param  {Object} point {x: 0, y: 0}
 */
SvgPanZoom.prototype.pan = function(point) {
  var viewportCTM = this.viewport.getCTM()
  viewportCTM.e = point.x
  viewportCTM.f = point.y
  this.viewport.setCTM(viewportCTM)
}

/**
 * Relatively pan the graph by a specified rendered position vector
 *
 * @param  {Object} point {x: 0, y: 0}
 */
SvgPanZoom.prototype.panBy = function(point) {
  var viewportCTM = this.viewport.getCTM()
  viewportCTM.e += point.x
  viewportCTM.f += point.y
  this.viewport.setCTM(viewportCTM)
}

/**
 * Get pan vector
 *
 * @return {Object} {x: 0, y: 0}
 */
SvgPanZoom.prototype.getPan = function() {
  var state = this.viewport.getState()

  return {x: state.x, y: state.y}
}

/**
 * Recalculates cached svg dimensions and controls position
 */
SvgPanZoom.prototype.resize = function() {
  // Get dimensions
  var boundingClientRectNormalized = SvgUtils.getBoundingClientRectNormalized(this.svg)
  this.width = boundingClientRectNormalized.width
  this.height = boundingClientRectNormalized.height

  // Recalculate original state
  var viewport = this.viewport
  viewport.options.width = this.width
  viewport.options.height = this.height
  viewport.processCTM()

  // Reposition control icons by re-enabling them
  if (this.options.controlIconsEnabled) {
    this.getPublicInstance().disableControlIcons()
    this.getPublicInstance().enableControlIcons()
  }
}

/**
 * Unbind mouse events, free callbacks and destroy public instance
 */
SvgPanZoom.prototype.destroy = function() {
  var that = this

  // Free callbacks
  this.beforeZoom = null
  this.onZoom = null
  this.beforePan = null
  this.onPan = null
  this.onUpdatedCTM = null

  // Destroy custom event handlers
  if (this.options.customEventsHandler != null) { // jshint ignore:line
    this.options.customEventsHandler.destroy({
      svgElement: this.svg
    , eventsListenerElement: this.options.eventsListenerElement
    , instance: this.getPublicInstance()
    })
  }

  // Unbind eventListeners
  for (var event in this.eventListeners) {
    (this.options.eventsListenerElement || this.svg)
      .removeEventListener(event, this.eventListeners[event], false)
  }

  // Unbind wheelListener
  this.disableMouseWheelZoom()

  // Remove control icons
  this.getPublicInstance().disableControlIcons()

  // Reset zoom and pan
  this.reset()

  // Remove instance from instancesStore
  instancesStore = instancesStore.filter(function(instance){
    return instance.svg !== that.svg
  })

  // Delete options and its contents
  delete this.options

  // Delete viewport to make public shadow viewport functions uncallable
  delete this.viewport

  // Destroy public instance and rewrite getPublicInstance
  delete this.publicInstance
  delete this.pi
  this.getPublicInstance = function(){
    return null
  }
}

/**
 * Returns a public instance object
 *
 * @return {Object} Public instance object
 */
SvgPanZoom.prototype.getPublicInstance = function() {
  var that = this

  // Create cache
  if (!this.publicInstance) {
    this.publicInstance = this.pi = {
      // Pan
      enablePan: function() {that.options.panEnabled = true; return that.pi}
    , disablePan: function() {that.options.panEnabled = false; return that.pi}
    , isPanEnabled: function() {return !!that.options.panEnabled}
    , pan: function(point) {that.pan(point); return that.pi}
    , panBy: function(point) {that.panBy(point); return that.pi}
    , getPan: function() {return that.getPan()}
      // Pan event
    , setBeforePan: function(fn) {that.options.beforePan = fn === null ? null : Utils.proxy(fn, that.publicInstance); return that.pi}
    , setOnPan: function(fn) {that.options.onPan = fn === null ? null : Utils.proxy(fn, that.publicInstance); return that.pi}
      // Zoom and Control Icons
    , enableZoom: function() {that.options.zoomEnabled = true; return that.pi}
    , disableZoom: function() {that.options.zoomEnabled = false; return that.pi}
    , isZoomEnabled: function() {return !!that.options.zoomEnabled}
    , enableControlIcons: function() {
        if (!that.options.controlIconsEnabled) {
          that.options.controlIconsEnabled = true
          ControlIcons.enable(that)
        }
        return that.pi
      }
    , disableControlIcons: function() {
        if (that.options.controlIconsEnabled) {
          that.options.controlIconsEnabled = false;
          ControlIcons.disable(that)
        }
        return that.pi
      }
    , isControlIconsEnabled: function() {return !!that.options.controlIconsEnabled}
      // Double click zoom
    , enableDblClickZoom: function() {that.options.dblClickZoomEnabled = true; return that.pi}
    , disableDblClickZoom: function() {that.options.dblClickZoomEnabled = false; return that.pi}
    , isDblClickZoomEnabled: function() {return !!that.options.dblClickZoomEnabled}
      // Mouse wheel zoom
    , enableMouseWheelZoom: function() {that.enableMouseWheelZoom(); return that.pi}
    , disableMouseWheelZoom: function() {that.disableMouseWheelZoom(); return that.pi}
    , isMouseWheelZoomEnabled: function() {return !!that.options.mouseWheelZoomEnabled}
      // Zoom scale and bounds
    , setZoomScaleSensitivity: function(scale) {that.options.zoomScaleSensitivity = scale; return that.pi}
    , setMinZoom: function(zoom) {that.options.minZoom = zoom; return that.pi}
    , setMaxZoom: function(zoom) {that.options.maxZoom = zoom; return that.pi}
      // Zoom event
    , setBeforeZoom: function(fn) {that.options.beforeZoom = fn === null ? null : Utils.proxy(fn, that.publicInstance); return that.pi}
    , setOnZoom: function(fn) {that.options.onZoom = fn === null ? null : Utils.proxy(fn, that.publicInstance); return that.pi}
      // Zooming
    , zoom: function(scale) {that.publicZoom(scale, true); return that.pi}
    , zoomBy: function(scale) {that.publicZoom(scale, false); return that.pi}
    , zoomAtPoint: function(scale, point) {that.publicZoomAtPoint(scale, point, true); return that.pi}
    , zoomAtPointBy: function(scale, point) {that.publicZoomAtPoint(scale, point, false); return that.pi}
    , zoomIn: function() {this.zoomBy(1 + that.options.zoomScaleSensitivity); return that.pi}
    , zoomOut: function() {this.zoomBy(1 / (1 + that.options.zoomScaleSensitivity)); return that.pi}
    , getZoom: function() {return that.getRelativeZoom()}
      // CTM update
    , setOnUpdatedCTM: function(fn) {that.options.onUpdatedCTM = fn === null ? null : Utils.proxy(fn, that.publicInstance); return that.pi}
      // Reset
    , resetZoom: function() {that.resetZoom(); return that.pi}
    , resetPan: function() {that.resetPan(); return that.pi}
    , reset: function() {that.reset(); return that.pi}
      // Fit, Contain and Center
    , fit: function() {that.fit(); return that.pi}
    , contain: function() {that.contain(); return that.pi}
    , center: function() {that.center(); return that.pi}
      // Size and Resize
    , updateBBox: function() {that.updateBBox(); return that.pi}
    , resize: function() {that.resize(); return that.pi}
    , getSizes: function() {
        return {
          width: that.width
        , height: that.height
        , realZoom: that.getZoom()
        , viewBox: that.viewport.getViewBox()
        }
      }
      // Destroy
    , destroy: function() {that.destroy(); return that.pi}
    }
  }

  return this.publicInstance
}

/**
 * Stores pairs of instances of SvgPanZoom and SVG
 * Each pair is represented by an object {svg: SVGSVGElement, instance: SvgPanZoom}
 *
 * @type {Array}
 */
var instancesStore = []

var svgPanZoom = function(elementOrSelector, options){
  var svg = Utils.getSvg(elementOrSelector)

  if (svg === null) {
    return null
  } else {
    // Look for existent instance
    for(var i = instancesStore.length - 1; i >= 0; i--) {
      if (instancesStore[i].svg === svg) {
        return instancesStore[i].instance.getPublicInstance()
      }
    }

    // If instance not found - create one
    instancesStore.push({
      svg: svg
    , instance: new SvgPanZoom(svg, options)
    })

    // Return just pushed instance
    return instancesStore[instancesStore.length - 1].instance.getPublicInstance()
  }
}

module.exports = svgPanZoom;

},{"./control-icons":2,"./shadow-viewport":3,"./svg-utilities":5,"./uniwheel":6,"./utilities":7}],5:[function(require,module,exports){
var Utils = require('./utilities')
  , _browser = 'unknown'
  ;

// http://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
if (/*@cc_on!@*/false || !!document.documentMode) { // internet explorer
  _browser = 'ie';
}

module.exports = {
  svgNS:  'http://www.w3.org/2000/svg'
, xmlNS:  'http://www.w3.org/XML/1998/namespace'
, xmlnsNS:  'http://www.w3.org/2000/xmlns/'
, xlinkNS:  'http://www.w3.org/1999/xlink'
, evNS:  'http://www.w3.org/2001/xml-events'

  /**
   * Get svg dimensions: width and height
   *
   * @param  {SVGSVGElement} svg
   * @return {Object}     {width: 0, height: 0}
   */
, getBoundingClientRectNormalized: function(svg) {
    if (svg.clientWidth && svg.clientHeight) {
      return {width: svg.clientWidth, height: svg.clientHeight}
    } else if (!!svg.getBoundingClientRect()) {
      return svg.getBoundingClientRect();
    } else {
      throw new Error('Cannot get BoundingClientRect for SVG.');
    }
  }

  /**
   * Gets g element with class of "viewport" or creates it if it doesn't exist
   *
   * @param  {SVGSVGElement} svg
   * @return {SVGElement}     g (group) element
   */
, getOrCreateViewport: function(svg, selector) {
    var viewport = null

    if (Utils.isElement(selector)) {
      viewport = selector
    } else {
      viewport = svg.querySelector(selector)
    }

    // Check if there is just one main group in SVG
    if (!viewport) {
      var childNodes = Array.prototype.slice.call(svg.childNodes || svg.children).filter(function(el){
        return el.nodeName !== 'defs' && el.nodeName !== '#text'
      })

      // Node name should be SVGGElement and should have no transform attribute
      // Groups with transform are not used as viewport because it involves parsing of all transform possibilities
      if (childNodes.length === 1 && childNodes[0].nodeName === 'g' && childNodes[0].getAttribute('transform') === null) {
        viewport = childNodes[0]
      }
    }

    // If no favorable group element exists then create one
    if (!viewport) {
      var viewportId = 'viewport-' + new Date().toISOString().replace(/\D/g, '');
      viewport = document.createElementNS(this.svgNS, 'g');
      viewport.setAttribute('id', viewportId);

      // Internet Explorer (all versions?) can't use childNodes, but other browsers prefer (require?) using childNodes
      var svgChildren = svg.childNodes || svg.children;
      if (!!svgChildren && svgChildren.length > 0) {
        for (var i = svgChildren.length; i > 0; i--) {
          // Move everything into viewport except defs
          if (svgChildren[svgChildren.length - i].nodeName !== 'defs') {
            viewport.appendChild(svgChildren[svgChildren.length - i]);
          }
        }
      }
      svg.appendChild(viewport);
    }

    // Parse class names
    var classNames = [];
    if (viewport.getAttribute('class')) {
      classNames = viewport.getAttribute('class').split(' ')
    }

    // Set class (if not set already)
    if (!~classNames.indexOf('svg-pan-zoom_viewport')) {
      classNames.push('svg-pan-zoom_viewport')
      viewport.setAttribute('class', classNames.join(' '))
    }

    return viewport
  }

  /**
   * Set SVG attributes
   *
   * @param  {SVGSVGElement} svg
   */
  , setupSvgAttributes: function(svg) {
    // Setting default attributes
    svg.setAttribute('xmlns', this.svgNS);
    svg.setAttributeNS(this.xmlnsNS, 'xmlns:xlink', this.xlinkNS);
    svg.setAttributeNS(this.xmlnsNS, 'xmlns:ev', this.evNS);

    // Needed for Internet Explorer, otherwise the viewport overflows
    if (svg.parentNode !== null) {
      var style = svg.getAttribute('style') || '';
      if (style.toLowerCase().indexOf('overflow') === -1) {
        svg.setAttribute('style', 'overflow: hidden; ' + style);
      }
    }
  }

/**
 * How long Internet Explorer takes to finish updating its display (ms).
 */
, internetExplorerRedisplayInterval: 300

/**
 * Forces the browser to redisplay all SVG elements that rely on an
 * element defined in a 'defs' section. It works globally, for every
 * available defs element on the page.
 * The throttling is intentionally global.
 *
 * This is only needed for IE. It is as a hack to make markers (and 'use' elements?)
 * visible after pan/zoom when there are multiple SVGs on the page.
 * See bug report: https://connect.microsoft.com/IE/feedback/details/781964/
 * also see svg-pan-zoom issue: https://github.com/ariutta/svg-pan-zoom/issues/62
 */
, refreshDefsGlobal: Utils.throttle(function() {
    var allDefs = document.querySelectorAll('defs');
    var allDefsCount = allDefs.length;
    for (var i = 0; i < allDefsCount; i++) {
      var thisDefs = allDefs[i];
      thisDefs.parentNode.insertBefore(thisDefs, thisDefs);
    }
  }, this.internetExplorerRedisplayInterval)

  /**
   * Sets the current transform matrix of an element
   *
   * @param {SVGElement} element
   * @param {SVGMatrix} matrix  CTM
   * @param {SVGElement} defs
   */
, setCTM: function(element, matrix, defs) {
    var that = this
      , s = 'matrix(' + matrix.a + ',' + matrix.b + ',' + matrix.c + ',' + matrix.d + ',' + matrix.e + ',' + matrix.f + ')';

    element.setAttributeNS(null, 'transform', s);
    if ('transform' in element.style) {
      element.style.transform = s;
    } else if ('-ms-transform' in element.style) {
      element.style['-ms-transform'] = s;
    } else if ('-webkit-transform' in element.style) {
      element.style['-webkit-transform'] = s;
    }

    // IE has a bug that makes markers disappear on zoom (when the matrix "a" and/or "d" elements change)
    // see http://stackoverflow.com/questions/17654578/svg-marker-does-not-work-in-ie9-10
    // and http://srndolha.wordpress.com/2013/11/25/svg-line-markers-may-disappear-in-internet-explorer-11/
    if (_browser === 'ie' && !!defs) {
      // this refresh is intended for redisplaying the SVG during zooming
      defs.parentNode.insertBefore(defs, defs);
      // this refresh is intended for redisplaying the other SVGs on a page when panning a given SVG
      // it is also needed for the given SVG itself, on zoomEnd, if the SVG contains any markers that
      // are located under any other element(s).
      window.setTimeout(function() {
        that.refreshDefsGlobal();
      }, that.internetExplorerRedisplayInterval);
    }
  }

  /**
   * Instantiate an SVGPoint object with given event coordinates
   *
   * @param {Event} evt
   * @param  {SVGSVGElement} svg
   * @return {SVGPoint}     point
   */
, getEventPoint: function(evt, svg) {
    var point = svg.createSVGPoint()

    Utils.mouseAndTouchNormalize(evt, svg)

    point.x = evt.clientX
    point.y = evt.clientY

    return point
  }

  /**
   * Get SVG center point
   *
   * @param  {SVGSVGElement} svg
   * @return {SVGPoint}
   */
, getSvgCenterPoint: function(svg, width, height) {
    return this.createSVGPoint(svg, width / 2, height / 2)
  }

  /**
   * Create a SVGPoint with given x and y
   *
   * @param  {SVGSVGElement} svg
   * @param  {Number} x
   * @param  {Number} y
   * @return {SVGPoint}
   */
, createSVGPoint: function(svg, x, y) {
    var point = svg.createSVGPoint()
    point.x = x
    point.y = y

    return point
  }
}

},{"./utilities":7}],6:[function(require,module,exports){
// uniwheel 0.1.2 (customized)
// A unified cross browser mouse wheel event handler
// https://github.com/teemualap/uniwheel

module.exports = (function(){

  //Full details: https://developer.mozilla.org/en-US/docs/Web/Reference/Events/wheel

  var prefix = "", _addEventListener, _removeEventListener, onwheel, support, fns = [];

  // detect event model
  if ( window.addEventListener ) {
    _addEventListener = "addEventListener";
    _removeEventListener = "removeEventListener";
  } else {
    _addEventListener = "attachEvent";
    _removeEventListener = "detachEvent";
    prefix = "on";
  }

  // detect available wheel event
  support = "onwheel" in document.createElement("div") ? "wheel" : // Modern browsers support "wheel"
            document.onmousewheel !== undefined ? "mousewheel" : // Webkit and IE support at least "mousewheel"
            "DOMMouseScroll"; // let's assume that remaining browsers are older Firefox


  function createCallback(element,callback,capture) {

    var fn = function(originalEvent) {

      !originalEvent && ( originalEvent = window.event );

      // create a normalized event object
      var event = {
        // keep a ref to the original event object
        originalEvent: originalEvent,
        target: originalEvent.target || originalEvent.srcElement,
        type: "wheel",
        deltaMode: originalEvent.type == "MozMousePixelScroll" ? 0 : 1,
        deltaX: 0,
        delatZ: 0,
        preventDefault: function() {
          originalEvent.preventDefault ?
            originalEvent.preventDefault() :
            originalEvent.returnValue = false;
        }
      };

      // calculate deltaY (and deltaX) according to the event
      if ( support == "mousewheel" ) {
        event.deltaY = - 1/40 * originalEvent.wheelDelta;
        // Webkit also support wheelDeltaX
        originalEvent.wheelDeltaX && ( event.deltaX = - 1/40 * originalEvent.wheelDeltaX );
      } else {
        event.deltaY = originalEvent.detail;
      }

      // it's time to fire the callback
      return callback( event );

    };

    fns.push({
      element: element,
      fn: fn,
      capture: capture
    });

    return fn;
  }

  function getCallback(element,capture) {
    for (var i = 0; i < fns.length; i++) {
      if (fns[i].element === element && fns[i].capture === capture) {
        return fns[i].fn;
      }
    }
    return function(){};
  }

  function removeCallback(element,capture) {
    for (var i = 0; i < fns.length; i++) {
      if (fns[i].element === element && fns[i].capture === capture) {
        return fns.splice(i,1);
      }
    }
  }

  function _addWheelListener( elem, eventName, callback, useCapture ) {

    var cb;

    if (support === "wheel") {
      cb = callback;
    } else {
      cb = createCallback(elem,callback,useCapture);
    }

    elem[ _addEventListener ]( prefix + eventName, cb, useCapture || false );

  }

  function _removeWheelListener( elem, eventName, callback, useCapture ) {

    if (support === "wheel") {
      cb = callback;
    } else {
      cb = getCallback(elem,useCapture);
    }

    elem[ _removeEventListener ]( prefix + eventName, cb, useCapture || false );

    removeCallback(elem,useCapture);

  }

  function addWheelListener( elem, callback, useCapture ) {
    _addWheelListener( elem, support, callback, useCapture );

    // handle MozMousePixelScroll in older Firefox
    if( support == "DOMMouseScroll" ) {
        _addWheelListener( elem, "MozMousePixelScroll", callback, useCapture);
    }
  }

  function removeWheelListener(elem,callback,useCapture){
    _removeWheelListener(elem,support,callback,useCapture);

    // handle MozMousePixelScroll in older Firefox
    if( support == "DOMMouseScroll" ) {
        _removeWheelListener(elem, "MozMousePixelScroll", callback, useCapture);
    }
  }

  return {
    on: addWheelListener,
    off: removeWheelListener
  };

})();

},{}],7:[function(require,module,exports){
module.exports = {
  /**
   * Extends an object
   *
   * @param  {Object} target object to extend
   * @param  {Object} source object to take properties from
   * @return {Object}        extended object
   */
  extend: function(target, source) {
    target = target || {};
    for (var prop in source) {
      // Go recursively
      if (this.isObject(source[prop])) {
        target[prop] = this.extend(target[prop], source[prop])
      } else {
        target[prop] = source[prop]
      }
    }
    return target;
  }

  /**
   * Checks if an object is a DOM element
   *
   * @param  {Object}  o HTML element or String
   * @return {Boolean}   returns true if object is a DOM element
   */
, isElement: function(o){
    return (
      o instanceof HTMLElement || o instanceof SVGElement || o instanceof SVGSVGElement || //DOM2
      (o && typeof o === 'object' && o !== null && o.nodeType === 1 && typeof o.nodeName === 'string')
    );
  }

  /**
   * Checks if an object is an Object
   *
   * @param  {Object}  o Object
   * @return {Boolean}   returns true if object is an Object
   */
, isObject: function(o){
    return Object.prototype.toString.call(o) === '[object Object]';
  }

  /**
   * Checks if variable is Number
   *
   * @param  {Integer|Float}  n
   * @return {Boolean}   returns true if variable is Number
   */
, isNumber: function(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  /**
   * Search for an SVG element
   *
   * @param  {Object|String} elementOrSelector DOM Element or selector String
   * @return {Object|Null}                   SVG or null
   */
, getSvg: function(elementOrSelector) {
    var element
      , svg;

    if (!this.isElement(elementOrSelector)) {
      // If selector provided
      if (typeof elementOrSelector === 'string' || elementOrSelector instanceof String) {
        // Try to find the element
        element = document.querySelector(elementOrSelector)

        if (!element) {
          throw new Error('Provided selector did not find any elements. Selector: ' + elementOrSelector)
          return null
        }
      } else {
        throw new Error('Provided selector is not an HTML object nor String')
        return null
      }
    } else {
      element = elementOrSelector
    }

    if (element.tagName.toLowerCase() === 'svg') {
      svg = element;
    } else {
      if (element.tagName.toLowerCase() === 'object') {
        svg = element.contentDocument.documentElement;
      } else {
        if (element.tagName.toLowerCase() === 'embed') {
          svg = element.getSVGDocument().documentElement;
        } else {
          if (element.tagName.toLowerCase() === 'img') {
            throw new Error('Cannot script an SVG in an "img" element. Please use an "object" element or an in-line SVG.');
          } else {
            throw new Error('Cannot get SVG.');
          }
          return null
        }
      }
    }

    return svg
  }

  /**
   * Attach a given context to a function
   * @param  {Function} fn      Function
   * @param  {Object}   context Context
   * @return {Function}           Function with certain context
   */
, proxy: function(fn, context) {
    return function() {
      return fn.apply(context, arguments)
    }
  }

  /**
   * Returns object type
   * Uses toString that returns [object SVGPoint]
   * And than parses object type from string
   *
   * @param  {Object} o Any object
   * @return {String}   Object type
   */
, getType: function(o) {
    return Object.prototype.toString.apply(o).replace(/^\[object\s/, '').replace(/\]$/, '')
  }

  /**
   * If it is a touch event than add clientX and clientY to event object
   *
   * @param  {Event} evt
   * @param  {SVGSVGElement} svg
   */
, mouseAndTouchNormalize: function(evt, svg) {
    // If no cilentX and but touch objects are available
    if (evt.clientX === void 0 || evt.clientX === null) {
      // Fallback
      evt.clientX = 0
      evt.clientY = 0

      // If it is a touch event
      if (evt.changedTouches !== void 0 && evt.changedTouches.length) {
        // If touch event has changedTouches
        if (evt.changedTouches[0].clientX !== void 0) {
          evt.clientX = evt.changedTouches[0].clientX
          evt.clientY = evt.changedTouches[0].clientY
        }
        // If changedTouches has pageX attribute
        else if (evt.changedTouches[0].pageX !== void 0) {
          var rect = svg.getBoundingClientRect();

          evt.clientX = evt.changedTouches[0].pageX - rect.left
          evt.clientY = evt.changedTouches[0].pageY - rect.top
        }
      // If it is a custom event
      } else if (evt.originalEvent !== void 0) {
        if (evt.originalEvent.clientX !== void 0) {
          evt.clientX = evt.originalEvent.clientX
          evt.clientY = evt.originalEvent.clientY
        }
      }
    }
  }

  /**
   * Check if an event is a double click/tap
   * TODO: For touch gestures use a library (hammer.js) that takes in account other events
   * (touchmove and touchend). It should take in account tap duration and traveled distance
   *
   * @param  {Event}  evt
   * @param  {Event}  prevEvt Previous Event
   * @return {Boolean}
   */
, isDblClick: function(evt, prevEvt) {
    // Double click detected by browser
    if (evt.detail === 2) {
      return true;
    }
    // Try to compare events
    else if (prevEvt !== void 0 && prevEvt !== null) {
      var timeStampDiff = evt.timeStamp - prevEvt.timeStamp // should be lower than 250 ms
        , touchesDistance = Math.sqrt(Math.pow(evt.clientX - prevEvt.clientX, 2) + Math.pow(evt.clientY - prevEvt.clientY, 2))

      return timeStampDiff < 250 && touchesDistance < 10
    }

    // Nothing found
    return false;
  }

  /**
   * Returns current timestamp as an integer
   *
   * @return {Number}
   */
, now: Date.now || function() {
    return new Date().getTime();
  }

  // From underscore.
  // Returns a function, that, when invoked, will only be triggered at most once
  // during a given window of time. Normally, the throttled function will run
  // as much as it can, without ever going more than once per `wait` duration;
  // but if you'd like to disable the execution on the leading edge, pass
  // `{leading: false}`. To disable execution on the trailing edge, ditto.
// jscs:disable
// jshint ignore:start
, throttle: function(func, wait, options) {
    var that = this;
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options) options = {};
    var later = function() {
      previous = options.leading === false ? 0 : that.now();
      timeout = null;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    };
    return function() {
      var now = that.now();
      if (!previous && options.leading === false) previous = now;
      var remaining = wait - (now - previous);
      context = this;
      args = arguments;
      if (remaining <= 0 || remaining > wait) {
        clearTimeout(timeout);
        timeout = null;
        previous = now;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
      } else if (!timeout && options.trailing !== false) {
        timeout = setTimeout(later, remaining);
      }
      return result;
    };
  }
// jshint ignore:end
// jscs:enable

  /**
   * Create a requestAnimationFrame simulation
   *
   * @param  {Number|String} refreshRate
   * @return {Function}
   */
, createRequestAnimationFrame: function(refreshRate) {
    var timeout = null

    // Convert refreshRate to timeout
    if (refreshRate !== 'auto' && refreshRate < 60 && refreshRate > 1) {
      timeout = Math.floor(1000 / refreshRate)
    }

    if (timeout === null) {
      return window.requestAnimationFrame || requestTimeout(33)
    } else {
      return requestTimeout(timeout)
    }
  }
}

/**
 * Create a callback that will execute after a given timeout
 *
 * @param  {Function} timeout
 * @return {Function}
 */
function requestTimeout(timeout) {
  return function(callback) {
    window.setTimeout(callback, timeout)
  }
}

},{}]},{},[1]);

/*! pdfmake v0.2.18, @license MIT, @link http://pdfmake.org */
(function webpackUniversalModuleDefinition(root, factory) {
	if(typeof exports === 'object' && typeof module === 'object')
		module.exports = factory();
	else if(typeof define === 'function' && define.amd)
		define([], factory);
	else {
		var a = factory();
		for(var i in a) (typeof exports === 'object' ? exports : root)[i] = a[i];
	}
})(typeof self !== 'undefined' ? self : this, function() {
return /******/ (function() { // webpackBootstrap
/******/ 	var __webpack_modules__ = ({

/***/ 84490:
/***/ (function(__unused_webpack_module, exports) {

"use strict";


// The following break classes are handled by the pair table
exports.OP = 0; // Opening punctuation
exports.CL = 1; // Closing punctuation
exports.CP = 2; // Closing parenthesis
exports.QU = 3; // Ambiguous quotation
exports.GL = 4; // Glue
exports.NS = 5; // Non-starters
exports.EX = 6; // Exclamation/Interrogation
exports.SY = 7; // Symbols allowing break after
exports.IS = 8; // Infix separator
exports.PR = 9; // Prefix
exports.PO = 10; // Postfix
exports.NU = 11; // Numeric
exports.AL = 12; // Alphabetic
exports.HL = 13; // Hebrew Letter
exports.ID = 14; // Ideographic
exports.IN = 15; // Inseparable characters
exports.HY = 16; // Hyphen
exports.BA = 17; // Break after
exports.BB = 18; // Break before
exports.B2 = 19; // Break on either side (but not pair)
exports.ZW = 20; // Zero-width space
exports.CM = 21; // Combining marks
exports.WJ = 22; // Word joiner
exports.H2 = 23; // Hangul LV
exports.H3 = 24; // Hangul LVT
exports.JL = 25; // Hangul L Jamo
exports.JV = 26; // Hangul V Jamo
exports.JT = 27; // Hangul T Jamo
exports.RI = 28; // Regional Indicator
exports.EB = 29; // Emoji Base
exports.EM = 30; // Emoji Modifier
exports.ZWJ = 31; // Zero Width Joiner
exports.CB = 32; // Contingent break

// The following break classes are not handled by the pair table
exports.AI = 33; // Ambiguous (Alphabetic or Ideograph)
exports.BK = 34; // Break (mandatory)
exports.CJ = 35; // Conditional Japanese Starter
exports.CR = 36; // Carriage return
exports.LF = 37; // Line feed
exports.NL = 38; // Next line
exports.SA = 39; // South-East Asian
exports.SG = 40; // Surrogates
exports.SP = 41; // Space
exports.XX = 42; // Unknown

/***/ }),

/***/ 5303:
/***/ (function(__unused_webpack_module, exports) {

"use strict";


var CI_BRK, CP_BRK, DI_BRK, IN_BRK, PR_BRK;
exports.DI_BRK = DI_BRK = 0; // Direct break opportunity
exports.IN_BRK = IN_BRK = 1; // Indirect break opportunity
exports.CI_BRK = CI_BRK = 2; // Indirect break opportunity for combining marks
exports.CP_BRK = CP_BRK = 3; // Prohibited break for combining marks
exports.PR_BRK = PR_BRK = 4; // Prohibited break

// Based on example pair table from https://www.unicode.org/reports/tr14/tr14-37.html#Table2
// - ZWJ special processing for LB8a of Revision 41
// - CB manually added as per Rule LB20
// - CL, CP, NS, SY, IS, PR, PO, HY, BA, B2 and RI manually adjusted as per LB22 of Revision 45
exports.pairTable = [
//OP   , CL    , CP    , QU    , GL    , NS    , EX    , SY    , IS    , PR    , PO    , NU    , AL    , HL    , ID    , IN    , HY    , BA    , BB    , B2    , ZW    , CM    , WJ    , H2    , H3    , JL    , JV    , JT    , RI    , EB    , EM    , ZWJ   , CB
[PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, CP_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK],
// OP
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// CL
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// CP
[PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK],
// QU
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK],
// GL
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// NS
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// EX
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// SY
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// IS
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK],
// PR
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// PO
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// NU
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// AL
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// HL
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// ID
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// IN
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, DI_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// HY
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, DI_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// BA
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK],
// BB
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, PR_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// B2
[DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK],
// ZW
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// CM
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK],
// WJ
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// H2
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// H3
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// JL
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// JV
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// JT
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// RI
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, DI_BRK],
// EB
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, IN_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// EM
[IN_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, PR_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, IN_BRK, IN_BRK, IN_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK],
// ZWJ
[DI_BRK, PR_BRK, PR_BRK, IN_BRK, IN_BRK, DI_BRK, PR_BRK, PR_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, PR_BRK, CI_BRK, PR_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, DI_BRK, IN_BRK, DI_BRK] // CB
];

/***/ }),

/***/ 62420:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


__webpack_require__(18756);
exports.EncodeStream = __webpack_require__(95291);
exports.DecodeStream = __webpack_require__(69591);
exports.Array = __webpack_require__(68332);
exports.LazyArray = __webpack_require__(11610);
exports.Bitfield = __webpack_require__(60480);
exports.Boolean = __webpack_require__(75041);
exports.Buffer = __webpack_require__(56711);
exports.Enum = __webpack_require__(36316);
exports.Optional = __webpack_require__(64241);
exports.Reserved = __webpack_require__(13549);
exports.String = __webpack_require__(52676);
exports.Struct = __webpack_require__(88152);
exports.VersionedStruct = __webpack_require__(99757);
var utils = __webpack_require__(18128);
var NumberT = __webpack_require__(56128);
var Pointer = __webpack_require__(9324);
Object.assign(exports, utils, NumberT, Pointer);

/***/ }),

/***/ 68332:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(65292);
__webpack_require__(73844);
__webpack_require__(69330);
__webpack_require__(11765);
__webpack_require__(81755);
__webpack_require__(20731);
__webpack_require__(24863);
__webpack_require__(14032);
__webpack_require__(61726);
__webpack_require__(57114);
__webpack_require__(58281);
__webpack_require__(94712);
function _createForOfIteratorHelperLoose(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (t) return (t = t.call(r)).next.bind(t); if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var o = 0; return function () { return o >= r.length ? { done: !0 } : { done: !1, value: r[o++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
var _require = __webpack_require__(56128),
  NumberT = _require.Number;
var utils = __webpack_require__(18128);
var ArrayT = /*#__PURE__*/function () {
  function ArrayT(type, length, lengthType) {
    if (lengthType === void 0) {
      lengthType = 'count';
    }
    this.type = type;
    this.length = length;
    this.lengthType = lengthType;
  }
  var _proto = ArrayT.prototype;
  _proto.decode = function decode(stream, parent) {
    var length;
    var pos = stream.pos;
    var res = [];
    var ctx = parent;
    if (this.length != null) {
      length = utils.resolveLength(this.length, stream, parent);
    }
    if (this.length instanceof NumberT) {
      // define hidden properties
      Object.defineProperties(res, {
        parent: {
          value: parent
        },
        _startOffset: {
          value: pos
        },
        _currentOffset: {
          value: 0,
          writable: true
        },
        _length: {
          value: length
        }
      });
      ctx = res;
    }
    if (length == null || this.lengthType === 'bytes') {
      var target = length != null ? stream.pos + length : (parent != null ? parent._length : undefined) ? parent._startOffset + parent._length : stream.length;
      while (stream.pos < target) {
        res.push(this.type.decode(stream, ctx));
      }
    } else {
      for (var i = 0, end = length; i < end; i++) {
        res.push(this.type.decode(stream, ctx));
      }
    }
    return res;
  };
  _proto.size = function size(array, ctx) {
    if (!array) {
      return this.type.size(null, ctx) * utils.resolveLength(this.length, null, ctx);
    }
    var size = 0;
    if (this.length instanceof NumberT) {
      size += this.length.size();
      ctx = {
        parent: ctx
      };
    }
    for (var _iterator = _createForOfIteratorHelperLoose(array), _step; !(_step = _iterator()).done;) {
      var item = _step.value;
      size += this.type.size(item, ctx);
    }
    return size;
  };
  _proto.encode = function encode(stream, array, parent) {
    var ctx = parent;
    if (this.length instanceof NumberT) {
      ctx = {
        pointers: [],
        startOffset: stream.pos,
        parent: parent
      };
      ctx.pointerOffset = stream.pos + this.size(array, ctx);
      this.length.encode(stream, array.length);
    }
    for (var _iterator2 = _createForOfIteratorHelperLoose(array), _step2; !(_step2 = _iterator2()).done;) {
      var item = _step2.value;
      this.type.encode(stream, item, ctx);
    }
    if (this.length instanceof NumberT) {
      var i = 0;
      while (i < ctx.pointers.length) {
        var ptr = ctx.pointers[i++];
        ptr.type.encode(stream, ptr.val);
      }
    }
  };
  return ArrayT;
}();
module.exports = ArrayT;

/***/ }),

/***/ 60480:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(74516);
var Bitfield = /*#__PURE__*/function () {
  function Bitfield(type, flags) {
    if (flags === void 0) {
      flags = [];
    }
    this.type = type;
    this.flags = flags;
  }
  var _proto = Bitfield.prototype;
  _proto.decode = function decode(stream) {
    var val = this.type.decode(stream);
    var res = {};
    for (var i = 0; i < this.flags.length; i++) {
      var flag = this.flags[i];
      if (flag != null) {
        res[flag] = !!(val & 1 << i);
      }
    }
    return res;
  };
  _proto.size = function size() {
    return this.type.size();
  };
  _proto.encode = function encode(stream, keys) {
    var val = 0;
    for (var i = 0; i < this.flags.length; i++) {
      var flag = this.flags[i];
      if (flag != null) {
        if (keys[flag]) {
          val |= 1 << i;
        }
      }
    }
    return this.type.encode(stream, val);
  };
  return Bitfield;
}();
module.exports = Bitfield;

/***/ }),

/***/ 75041:
/***/ (function(module) {

"use strict";


var BooleanT = /*#__PURE__*/function () {
  function BooleanT(type) {
    this.type = type;
  }
  var _proto = BooleanT.prototype;
  _proto.decode = function decode(stream, parent) {
    return !!this.type.decode(stream, parent);
  };
  _proto.size = function size(val, parent) {
    return this.type.size(val, parent);
  };
  _proto.encode = function encode(stream, val, parent) {
    return this.type.encode(stream, +val, parent);
  };
  return BooleanT;
}();
module.exports = BooleanT;

/***/ }),

/***/ 56711:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var utils = __webpack_require__(18128);
var _require = __webpack_require__(56128),
  NumberT = _require.Number;
var BufferT = /*#__PURE__*/function () {
  function BufferT(length) {
    this.length = length;
  }
  var _proto = BufferT.prototype;
  _proto.decode = function decode(stream, parent) {
    var length = utils.resolveLength(this.length, stream, parent);
    return stream.readBuffer(length);
  };
  _proto.size = function size(val, parent) {
    if (!val) {
      return utils.resolveLength(this.length, null, parent);
    }
    return val.length;
  };
  _proto.encode = function encode(stream, buf, parent) {
    if (this.length instanceof NumberT) {
      this.length.encode(stream, buf.length);
    }
    return stream.writeBuffer(buf);
  };
  return BufferT;
}();
module.exports = BufferT;

/***/ }),

/***/ 69591:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


__webpack_require__(20731);
__webpack_require__(14032);
__webpack_require__(61726);
__webpack_require__(57114);
__webpack_require__(46467);
var iconv;
try {
  iconv = __webpack_require__(54171);
} catch (error) {}
var DecodeStream = /*#__PURE__*/function () {
  function DecodeStream(buffer) {
    this.buffer = buffer;
    this.pos = 0;
    this.length = this.buffer.length;
  }
  var _proto = DecodeStream.prototype;
  _proto.readString = function readString(length, encoding) {
    if (encoding === void 0) {
      encoding = 'ascii';
    }
    switch (encoding) {
      case 'utf16le':
      case 'ucs2':
      case 'utf8':
      case 'ascii':
        return this.buffer.toString(encoding, this.pos, this.pos += length);
      case 'utf16be':
        var buf = Buffer.from(this.readBuffer(length));

        // swap the bytes
        for (var i = 0, end = buf.length - 1; i < end; i += 2) {
          var byte = buf[i];
          buf[i] = buf[i + 1];
          buf[i + 1] = byte;
        }
        return buf.toString('utf16le');
      default:
        buf = this.readBuffer(length);
        if (iconv) {
          try {
            return iconv.decode(buf, encoding);
          } catch (error1) {}
        }
        return buf;
    }
  };
  _proto.readBuffer = function readBuffer(length) {
    return this.buffer.slice(this.pos, this.pos += length);
  };
  _proto.readUInt24BE = function readUInt24BE() {
    return (this.readUInt16BE() << 8) + this.readUInt8();
  };
  _proto.readUInt24LE = function readUInt24LE() {
    return this.readUInt16LE() + (this.readUInt8() << 16);
  };
  _proto.readInt24BE = function readInt24BE() {
    return (this.readInt16BE() << 8) + this.readUInt8();
  };
  _proto.readInt24LE = function readInt24LE() {
    return this.readUInt16LE() + (this.readInt8() << 16);
  };
  return DecodeStream;
}();
DecodeStream.TYPES = {
  UInt8: 1,
  UInt16: 2,
  UInt24: 3,
  UInt32: 4,
  Int8: 1,
  Int16: 2,
  Int24: 3,
  Int32: 4,
  Float: 4,
  Double: 8
};
var _loop = function _loop(key) {
  if (key.slice(0, 4) === 'read') {
    var bytes = DecodeStream.TYPES[key.replace(/read|[BL]E/g, '')];
    DecodeStream.prototype[key] = function () {
      var ret = this.buffer[key](this.pos);
      this.pos += bytes;
      return ret;
    };
  }
};
for (var key in Buffer.prototype) {
  _loop(key);
}
module.exports = DecodeStream;

/***/ }),

/***/ 95291:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


__webpack_require__(41584);
__webpack_require__(20731);
__webpack_require__(61726);
__webpack_require__(46467);
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
var iconv;
var stream = __webpack_require__(9760);
var DecodeStream = __webpack_require__(69591);
try {
  iconv = __webpack_require__(54171);
} catch (error) {}
var EncodeStream = /*#__PURE__*/function (_stream$Readable) {
  function EncodeStream(bufferSize) {
    var _this;
    if (bufferSize === void 0) {
      bufferSize = 65536;
    }
    _this = _stream$Readable.apply(this, arguments) || this;
    _this.buffer = Buffer.alloc(bufferSize);
    _this.bufferOffset = 0;
    _this.pos = 0;
    return _this;
  }

  // do nothing, required by node
  _inheritsLoose(EncodeStream, _stream$Readable);
  var _proto = EncodeStream.prototype;
  _proto._read = function _read() {};
  _proto.ensure = function ensure(bytes) {
    if (this.bufferOffset + bytes > this.buffer.length) {
      return this.flush();
    }
  };
  _proto.flush = function flush() {
    if (this.bufferOffset > 0) {
      this.push(Buffer.from(this.buffer.slice(0, this.bufferOffset)));
      return this.bufferOffset = 0;
    }
  };
  _proto.writeBuffer = function writeBuffer(buffer) {
    this.flush();
    this.push(buffer);
    return this.pos += buffer.length;
  };
  _proto.writeString = function writeString(string, encoding) {
    if (encoding === void 0) {
      encoding = 'ascii';
    }
    switch (encoding) {
      case 'utf16le':
      case 'ucs2':
      case 'utf8':
      case 'ascii':
        return this.writeBuffer(Buffer.from(string, encoding));
      case 'utf16be':
        var buf = Buffer.from(string, 'utf16le');

        // swap the bytes
        for (var i = 0, end = buf.length - 1; i < end; i += 2) {
          var byte = buf[i];
          buf[i] = buf[i + 1];
          buf[i + 1] = byte;
        }
        return this.writeBuffer(buf);
      default:
        if (iconv) {
          return this.writeBuffer(iconv.encode(string, encoding));
        } else {
          throw new Error('Install iconv-lite to enable additional string encodings.');
        }
    }
  };
  _proto.writeUInt24BE = function writeUInt24BE(val) {
    this.ensure(3);
    this.buffer[this.bufferOffset++] = val >>> 16 & 0xff;
    this.buffer[this.bufferOffset++] = val >>> 8 & 0xff;
    this.buffer[this.bufferOffset++] = val & 0xff;
    return this.pos += 3;
  };
  _proto.writeUInt24LE = function writeUInt24LE(val) {
    this.ensure(3);
    this.buffer[this.bufferOffset++] = val & 0xff;
    this.buffer[this.bufferOffset++] = val >>> 8 & 0xff;
    this.buffer[this.bufferOffset++] = val >>> 16 & 0xff;
    return this.pos += 3;
  };
  _proto.writeInt24BE = function writeInt24BE(val) {
    if (val >= 0) {
      return this.writeUInt24BE(val);
    } else {
      return this.writeUInt24BE(val + 0xffffff + 1);
    }
  };
  _proto.writeInt24LE = function writeInt24LE(val) {
    if (val >= 0) {
      return this.writeUInt24LE(val);
    } else {
      return this.writeUInt24LE(val + 0xffffff + 1);
    }
  };
  _proto.fill = function fill(val, length) {
    if (length < this.buffer.length) {
      this.ensure(length);
      this.buffer.fill(val, this.bufferOffset, this.bufferOffset + length);
      this.bufferOffset += length;
      return this.pos += length;
    } else {
      var buf = Buffer.alloc(length);
      buf.fill(val);
      return this.writeBuffer(buf);
    }
  };
  _proto.end = function end() {
    this.flush();
    return this.push(null);
  };
  return EncodeStream;
}(stream.Readable);
var _loop = function _loop(key) {
  if (key.slice(0, 5) === 'write') {
    var bytes = +DecodeStream.TYPES[key.replace(/write|[BL]E/g, '')];
    EncodeStream.prototype[key] = function (value) {
      this.ensure(bytes);
      this.buffer[key](value, this.bufferOffset);
      this.bufferOffset += bytes;
      return this.pos += bytes;
    };
  }
};
for (var key in Buffer.prototype) {
  _loop(key);
}
module.exports = EncodeStream;

/***/ }),

/***/ 36316:
/***/ (function(module) {

"use strict";


var Enum = /*#__PURE__*/function () {
  function Enum(type, options) {
    if (options === void 0) {
      options = [];
    }
    this.type = type;
    this.options = options;
  }
  var _proto = Enum.prototype;
  _proto.decode = function decode(stream) {
    var index = this.type.decode(stream);
    return this.options[index] || index;
  };
  _proto.size = function size() {
    return this.type.size();
  };
  _proto.encode = function encode(stream, val) {
    var index = this.options.indexOf(val);
    if (index === -1) {
      throw new Error("Unknown option in enum: " + val);
    }
    return this.type.encode(stream, index);
  };
  return Enum;
}();
module.exports = Enum;

/***/ }),

/***/ 11610:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(14032);
__webpack_require__(68067);
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
var ArrayT = __webpack_require__(68332);
var _require = __webpack_require__(56128),
  NumberT = _require.Number;
var utils = __webpack_require__(18128);
var _require2 = __webpack_require__(7187),
  _inspect = _require2.inspect;
var LazyArrayT = /*#__PURE__*/function (_ArrayT) {
  function LazyArrayT() {
    return _ArrayT.apply(this, arguments) || this;
  }
  _inheritsLoose(LazyArrayT, _ArrayT);
  var _proto = LazyArrayT.prototype;
  _proto.decode = function decode(stream, parent) {
    var pos = stream.pos;
    var length = utils.resolveLength(this.length, stream, parent);
    if (this.length instanceof NumberT) {
      parent = {
        parent: parent,
        _startOffset: pos,
        _currentOffset: 0,
        _length: length
      };
    }
    var res = new LazyArray(this.type, length, stream, parent);
    stream.pos += length * this.type.size(null, parent);
    return res;
  };
  _proto.size = function size(val, ctx) {
    if (val instanceof LazyArray) {
      val = val.toArray();
    }
    return _ArrayT.prototype.size.call(this, val, ctx);
  };
  _proto.encode = function encode(stream, val, ctx) {
    if (val instanceof LazyArray) {
      val = val.toArray();
    }
    return _ArrayT.prototype.encode.call(this, stream, val, ctx);
  };
  return LazyArrayT;
}(ArrayT);
var LazyArray = /*#__PURE__*/function () {
  function LazyArray(type, length, stream, ctx) {
    this.type = type;
    this.length = length;
    this.stream = stream;
    this.ctx = ctx;
    this.base = this.stream.pos;
    this.items = [];
  }
  var _proto2 = LazyArray.prototype;
  _proto2.get = function get(index) {
    if (index < 0 || index >= this.length) {
      return undefined;
    }
    if (this.items[index] == null) {
      var pos = this.stream.pos;
      this.stream.pos = this.base + this.type.size(null, this.ctx) * index;
      this.items[index] = this.type.decode(this.stream, this.ctx);
      this.stream.pos = pos;
    }
    return this.items[index];
  };
  _proto2.toArray = function toArray() {
    var result = [];
    for (var i = 0, end = this.length; i < end; i++) {
      result.push(this.get(i));
    }
    return result;
  };
  _proto2.inspect = function inspect() {
    return _inspect(this.toArray());
  };
  return LazyArray;
}();
module.exports = LazyArrayT;

/***/ }),

/***/ 56128:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
var DecodeStream = __webpack_require__(69591);
var NumberT = /*#__PURE__*/function () {
  function NumberT(type, endian) {
    if (endian === void 0) {
      endian = 'BE';
    }
    this.type = type;
    this.endian = endian;
    this.fn = this.type;
    if (this.type[this.type.length - 1] !== '8') {
      this.fn += this.endian;
    }
  }
  var _proto = NumberT.prototype;
  _proto.size = function size() {
    return DecodeStream.TYPES[this.type];
  };
  _proto.decode = function decode(stream) {
    return stream["read" + this.fn]();
  };
  _proto.encode = function encode(stream, val) {
    return stream["write" + this.fn](val);
  };
  return NumberT;
}();
exports.Number = NumberT;
exports.uint8 = new NumberT('UInt8');
exports.uint16be = exports.uint16 = new NumberT('UInt16', 'BE');
exports.uint16le = new NumberT('UInt16', 'LE');
exports.uint24be = exports.uint24 = new NumberT('UInt24', 'BE');
exports.uint24le = new NumberT('UInt24', 'LE');
exports.uint32be = exports.uint32 = new NumberT('UInt32', 'BE');
exports.uint32le = new NumberT('UInt32', 'LE');
exports.int8 = new NumberT('Int8');
exports.int16be = exports.int16 = new NumberT('Int16', 'BE');
exports.int16le = new NumberT('Int16', 'LE');
exports.int24be = exports.int24 = new NumberT('Int24', 'BE');
exports.int24le = new NumberT('Int24', 'LE');
exports.int32be = exports.int32 = new NumberT('Int32', 'BE');
exports.int32le = new NumberT('Int32', 'LE');
exports.floatbe = exports.float = new NumberT('Float', 'BE');
exports.floatle = new NumberT('Float', 'LE');
exports.doublebe = exports.double = new NumberT('Double', 'BE');
exports.doublele = new NumberT('Double', 'LE');
var Fixed = /*#__PURE__*/function (_NumberT) {
  function Fixed(size, endian, fracBits) {
    var _this;
    if (fracBits === void 0) {
      fracBits = size >> 1;
    }
    _this = _NumberT.call(this, "Int" + size, endian) || this;
    _this._point = 1 << fracBits;
    return _this;
  }
  _inheritsLoose(Fixed, _NumberT);
  var _proto2 = Fixed.prototype;
  _proto2.decode = function decode(stream) {
    return _NumberT.prototype.decode.call(this, stream) / this._point;
  };
  _proto2.encode = function encode(stream, val) {
    return _NumberT.prototype.encode.call(this, stream, val * this._point | 0);
  };
  return Fixed;
}(NumberT);
exports.Fixed = Fixed;
exports.fixed16be = exports.fixed16 = new Fixed(16, 'BE');
exports.fixed16le = new Fixed(16, 'LE');
exports.fixed32be = exports.fixed32 = new Fixed(32, 'BE');
exports.fixed32le = new Fixed(32, 'LE');

/***/ }),

/***/ 64241:
/***/ (function(module) {

"use strict";


var Optional = /*#__PURE__*/function () {
  function Optional(type, condition) {
    if (condition === void 0) {
      condition = true;
    }
    this.type = type;
    this.condition = condition;
  }
  var _proto = Optional.prototype;
  _proto.decode = function decode(stream, parent) {
    var condition = this.condition;
    if (typeof condition === 'function') {
      condition = condition.call(parent, parent);
    }
    if (condition) {
      return this.type.decode(stream, parent);
    }
  };
  _proto.size = function size(val, parent) {
    var condition = this.condition;
    if (typeof condition === 'function') {
      condition = condition.call(parent, parent);
    }
    if (condition) {
      return this.type.size(val, parent);
    } else {
      return 0;
    }
  };
  _proto.encode = function encode(stream, val, parent) {
    var condition = this.condition;
    if (typeof condition === 'function') {
      condition = condition.call(parent, parent);
    }
    if (condition) {
      return this.type.encode(stream, val, parent);
    }
  };
  return Optional;
}();
module.exports = Optional;

/***/ }),

/***/ 9324:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


var utils = __webpack_require__(18128);
var Pointer = /*#__PURE__*/function () {
  function Pointer(offsetType, type, options) {
    if (options === void 0) {
      options = {};
    }
    this.offsetType = offsetType;
    this.type = type;
    this.options = options;
    if (this.type === 'void') {
      this.type = null;
    }
    if (this.options.type == null) {
      this.options.type = 'local';
    }
    if (this.options.allowNull == null) {
      this.options.allowNull = true;
    }
    if (this.options.nullValue == null) {
      this.options.nullValue = 0;
    }
    if (this.options.lazy == null) {
      this.options.lazy = false;
    }
    if (this.options.relativeTo) {
      if (typeof this.options.relativeTo !== 'function') {
        throw new Error('relativeTo option must be a function');
      }
      this.relativeToGetter = options.relativeTo;
    }
  }
  var _proto = Pointer.prototype;
  _proto.decode = function decode(stream, ctx) {
    var _this = this;
    var offset = this.offsetType.decode(stream, ctx);

    // handle NULL pointers
    if (offset === this.options.nullValue && this.options.allowNull) {
      return null;
    }
    var relative;
    switch (this.options.type) {
      case 'local':
        relative = ctx._startOffset;
        break;
      case 'immediate':
        relative = stream.pos - this.offsetType.size();
        break;
      case 'parent':
        relative = ctx.parent._startOffset;
        break;
      default:
        var c = ctx;
        while (c.parent) {
          c = c.parent;
        }
        relative = c._startOffset || 0;
    }
    if (this.options.relativeTo) {
      relative += this.relativeToGetter(ctx);
    }
    var ptr = offset + relative;
    if (this.type != null) {
      var val = null;
      var decodeValue = function decodeValue() {
        if (val != null) {
          return val;
        }
        var pos = stream.pos;
        stream.pos = ptr;
        val = _this.type.decode(stream, ctx);
        stream.pos = pos;
        return val;
      };

      // If this is a lazy pointer, define a getter to decode only when needed.
      // This obviously only works when the pointer is contained by a Struct.
      if (this.options.lazy) {
        return new utils.PropertyDescriptor({
          get: decodeValue
        });
      }
      return decodeValue();
    } else {
      return ptr;
    }
  };
  _proto.size = function size(val, ctx) {
    var parent = ctx;
    switch (this.options.type) {
      case 'local':
      case 'immediate':
        break;
      case 'parent':
        ctx = ctx.parent;
        break;
      default:
        // global
        while (ctx.parent) {
          ctx = ctx.parent;
        }
    }
    var type = this.type;
    if (type == null) {
      if (!(val instanceof VoidPointer)) {
        throw new Error("Must be a VoidPointer");
      }
      var _val = val;
      type = _val.type;
      val = val.value;
    }
    if (val && ctx) {
      ctx.pointerSize += type.size(val, parent);
    }
    return this.offsetType.size();
  };
  _proto.encode = function encode(stream, val, ctx) {
    var relative;
    var parent = ctx;
    if (val == null) {
      this.offsetType.encode(stream, this.options.nullValue);
      return;
    }
    switch (this.options.type) {
      case 'local':
        relative = ctx.startOffset;
        break;
      case 'immediate':
        relative = stream.pos + this.offsetType.size(val, parent);
        break;
      case 'parent':
        ctx = ctx.parent;
        relative = ctx.startOffset;
        break;
      default:
        // global
        relative = 0;
        while (ctx.parent) {
          ctx = ctx.parent;
        }
    }
    if (this.options.relativeTo) {
      relative += this.relativeToGetter(parent.val);
    }
    this.offsetType.encode(stream, ctx.pointerOffset - relative);
    var type = this.type;
    if (type == null) {
      if (!(val instanceof VoidPointer)) {
        throw new Error("Must be a VoidPointer");
      }
      var _val2 = val;
      type = _val2.type;
      val = val.value;
    }
    ctx.pointers.push({
      type: type,
      val: val,
      parent: parent
    });
    return ctx.pointerOffset += type.size(val, parent);
  };
  return Pointer;
}(); // A pointer whose type is determined at decode time
var VoidPointer = function VoidPointer(type, value) {
  this.type = type;
  this.value = value;
};
exports.Pointer = Pointer;
exports.VoidPointer = VoidPointer;

/***/ }),

/***/ 13549:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(41584);
var utils = __webpack_require__(18128);
var Reserved = /*#__PURE__*/function () {
  function Reserved(type, count) {
    if (count === void 0) {
      count = 1;
    }
    this.type = type;
    this.count = count;
  }
  var _proto = Reserved.prototype;
  _proto.decode = function decode(stream, parent) {
    stream.pos += this.size(null, parent);
    return undefined;
  };
  _proto.size = function size(data, parent) {
    var count = utils.resolveLength(this.count, null, parent);
    return this.type.size() * count;
  };
  _proto.encode = function encode(stream, val, parent) {
    return stream.fill(0, this.size(val, parent));
  };
  return Reserved;
}();
module.exports = Reserved;

/***/ }),

/***/ 52676:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


var _require = __webpack_require__(56128),
  NumberT = _require.Number;
var utils = __webpack_require__(18128);
var StringT = /*#__PURE__*/function () {
  function StringT(length, encoding) {
    if (encoding === void 0) {
      encoding = 'ascii';
    }
    this.length = length;
    this.encoding = encoding;
  }
  var _proto = StringT.prototype;
  _proto.decode = function decode(stream, parent) {
    var length, pos;
    if (this.length != null) {
      length = utils.resolveLength(this.length, stream, parent);
    } else {
      var buffer;
      buffer = stream.buffer;
      length = stream.length;
      pos = stream.pos;
      while (pos < length && buffer[pos] !== 0x00) {
        ++pos;
      }
      length = pos - stream.pos;
    }
    var encoding = this.encoding;
    if (typeof encoding === 'function') {
      encoding = encoding.call(parent, parent) || 'ascii';
    }
    var string = stream.readString(length, encoding);
    if (this.length == null && stream.pos < stream.length) {
      stream.pos++;
    }
    return string;
  };
  _proto.size = function size(val, parent) {
    // Use the defined value if no value was given
    if (!val) {
      return utils.resolveLength(this.length, null, parent);
    }
    var encoding = this.encoding;
    if (typeof encoding === 'function') {
      encoding = encoding.call(parent != null ? parent.val : undefined, parent != null ? parent.val : undefined) || 'ascii';
    }
    if (encoding === 'utf16be') {
      encoding = 'utf16le';
    }
    var size = Buffer.byteLength(val, encoding);
    if (this.length instanceof NumberT) {
      size += this.length.size();
    }
    if (this.length == null) {
      size++;
    }
    return size;
  };
  _proto.encode = function encode(stream, val, parent) {
    var encoding = this.encoding;
    if (typeof encoding === 'function') {
      encoding = encoding.call(parent != null ? parent.val : undefined, parent != null ? parent.val : undefined) || 'ascii';
    }
    if (this.length instanceof NumberT) {
      this.length.encode(stream, Buffer.byteLength(val, encoding));
    }
    stream.writeString(val, encoding);
    if (this.length == null) {
      return stream.writeUInt8(0x00);
    }
  };
  return StringT;
}();
module.exports = StringT;

/***/ }),

/***/ 88152:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var utils = __webpack_require__(18128);
var Struct = /*#__PURE__*/function () {
  function Struct(fields) {
    if (fields === void 0) {
      fields = {};
    }
    this.fields = fields;
  }
  var _proto = Struct.prototype;
  _proto.decode = function decode(stream, parent, length) {
    if (length === void 0) {
      length = 0;
    }
    var res = this._setup(stream, parent, length);
    this._parseFields(stream, res, this.fields);
    if (this.process != null) {
      this.process.call(res, stream);
    }
    return res;
  };
  _proto._setup = function _setup(stream, parent, length) {
    var res = {};

    // define hidden properties
    Object.defineProperties(res, {
      parent: {
        value: parent
      },
      _startOffset: {
        value: stream.pos
      },
      _currentOffset: {
        value: 0,
        writable: true
      },
      _length: {
        value: length
      }
    });
    return res;
  };
  _proto._parseFields = function _parseFields(stream, res, fields) {
    for (var key in fields) {
      var val;
      var type = fields[key];
      if (typeof type === 'function') {
        val = type.call(res, res);
      } else {
        val = type.decode(stream, res);
      }
      if (val !== undefined) {
        if (val instanceof utils.PropertyDescriptor) {
          Object.defineProperty(res, key, val);
        } else {
          res[key] = val;
        }
      }
      res._currentOffset = stream.pos - res._startOffset;
    }
  };
  _proto.size = function size(val, parent, includePointers) {
    if (val == null) {
      val = {};
    }
    if (includePointers == null) {
      includePointers = true;
    }
    var ctx = {
      parent: parent,
      val: val,
      pointerSize: 0
    };
    var size = 0;
    for (var key in this.fields) {
      var type = this.fields[key];
      if (type.size != null) {
        size += type.size(val[key], ctx);
      }
    }
    if (includePointers) {
      size += ctx.pointerSize;
    }
    return size;
  };
  _proto.encode = function encode(stream, val, parent) {
    var type;
    if (this.preEncode != null) {
      this.preEncode.call(val, stream);
    }
    var ctx = {
      pointers: [],
      startOffset: stream.pos,
      parent: parent,
      val: val,
      pointerSize: 0
    };
    ctx.pointerOffset = stream.pos + this.size(val, ctx, false);
    for (var key in this.fields) {
      type = this.fields[key];
      if (type.encode != null) {
        type.encode(stream, val[key], ctx);
      }
    }
    var i = 0;
    while (i < ctx.pointers.length) {
      var ptr = ctx.pointers[i++];
      ptr.type.encode(stream, ptr.val, ptr.parent);
    }
  };
  return Struct;
}();
module.exports = Struct;

/***/ }),

/***/ 99757:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(14032);
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
var Struct = __webpack_require__(88152);
var getPath = function getPath(object, pathArray) {
  return pathArray.reduce(function (prevObj, key) {
    return prevObj && prevObj[key];
  }, object);
};
var VersionedStruct = /*#__PURE__*/function (_Struct) {
  function VersionedStruct(type, versions) {
    var _this;
    if (versions === void 0) {
      versions = {};
    }
    _this = _Struct.call(this) || this;
    _this.type = type;
    _this.versions = versions;
    if (typeof type === 'string') {
      _this.versionPath = type.split('.');
    }
    return _this;
  }
  _inheritsLoose(VersionedStruct, _Struct);
  var _proto = VersionedStruct.prototype;
  _proto.decode = function decode(stream, parent, length) {
    if (length === void 0) {
      length = 0;
    }
    var res = this._setup(stream, parent, length);
    if (typeof this.type === 'string') {
      res.version = getPath(parent, this.versionPath);
    } else {
      res.version = this.type.decode(stream);
    }
    if (this.versions.header) {
      this._parseFields(stream, res, this.versions.header);
    }
    var fields = this.versions[res.version];
    if (fields == null) {
      throw new Error("Unknown version " + res.version);
    }
    if (fields instanceof VersionedStruct) {
      return fields.decode(stream, parent);
    }
    this._parseFields(stream, res, fields);
    if (this.process != null) {
      this.process.call(res, stream);
    }
    return res;
  };
  _proto.size = function size(val, parent, includePointers) {
    if (includePointers === void 0) {
      includePointers = true;
    }
    var key, type;
    if (!val) {
      throw new Error('Not a fixed size');
    }
    var ctx = {
      parent: parent,
      val: val,
      pointerSize: 0
    };
    var size = 0;
    if (typeof this.type !== 'string') {
      size += this.type.size(val.version, ctx);
    }
    if (this.versions.header) {
      for (key in this.versions.header) {
        type = this.versions.header[key];
        if (type.size != null) {
          size += type.size(val[key], ctx);
        }
      }
    }
    var fields = this.versions[val.version];
    if (fields == null) {
      throw new Error("Unknown version " + val.version);
    }
    for (key in fields) {
      type = fields[key];
      if (type.size != null) {
        size += type.size(val[key], ctx);
      }
    }
    if (includePointers) {
      size += ctx.pointerSize;
    }
    return size;
  };
  _proto.encode = function encode(stream, val, parent) {
    var key, type;
    if (this.preEncode != null) {
      this.preEncode.call(val, stream);
    }
    var ctx = {
      pointers: [],
      startOffset: stream.pos,
      parent: parent,
      val: val,
      pointerSize: 0
    };
    ctx.pointerOffset = stream.pos + this.size(val, ctx, false);
    if (typeof this.type !== 'string') {
      this.type.encode(stream, val.version);
    }
    if (this.versions.header) {
      for (key in this.versions.header) {
        type = this.versions.header[key];
        if (type.encode != null) {
          type.encode(stream, val[key], ctx);
        }
      }
    }
    var fields = this.versions[val.version];
    for (key in fields) {
      type = fields[key];
      if (type.encode != null) {
        type.encode(stream, val[key], ctx);
      }
    }
    var i = 0;
    while (i < ctx.pointers.length) {
      var ptr = ctx.pointers[i++];
      ptr.type.encode(stream, ptr.val, ptr.parent);
    }
  };
  return VersionedStruct;
}(Struct);
module.exports = VersionedStruct;

/***/ }),

/***/ 18128:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


var _require = __webpack_require__(56128),
  NumberT = _require.Number;
exports.resolveLength = function (length, stream, parent) {
  var res;
  if (typeof length === 'number') {
    res = length;
  } else if (typeof length === 'function') {
    res = length.call(parent, parent);
  } else if (parent && typeof length === 'string') {
    res = parent[length];
  } else if (stream && length instanceof NumberT) {
    res = length.decode(stream);
  }
  if (isNaN(res)) {
    throw new Error('Not a fixed size');
  }
  return res;
};
var PropertyDescriptor = function PropertyDescriptor(opts) {
  if (opts === void 0) {
    opts = {};
  }
  this.enumerable = true;
  this.configurable = true;
  for (var key in opts) {
    var val = opts[key];
    this[key] = val;
  }
};
exports.PropertyDescriptor = PropertyDescriptor;

/***/ }),

/***/ 14598:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <https://feross.org>
 * @license  MIT
 */
/* eslint-disable no-proto */



__webpack_require__(65292);
__webpack_require__(73844);
__webpack_require__(5597);
__webpack_require__(39081);
__webpack_require__(68626);
__webpack_require__(41584);
__webpack_require__(58028);
__webpack_require__(81755);
__webpack_require__(20731);
__webpack_require__(8953);
__webpack_require__(7585);
__webpack_require__(24863);
__webpack_require__(7283);
__webpack_require__(2876);
__webpack_require__(14032);
__webpack_require__(61726);
__webpack_require__(57114);
__webpack_require__(47458);
__webpack_require__(46467);
__webpack_require__(72095);
__webpack_require__(56912);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
var base64 = __webpack_require__(32504);
var ieee754 = __webpack_require__(89029);
var customInspectSymbol = typeof Symbol === 'function' && typeof Symbol['for'] === 'function' // eslint-disable-line dot-notation
? Symbol['for']('nodejs.util.inspect.custom') // eslint-disable-line dot-notation
: null;
exports.Buffer = Buffer;
exports.SlowBuffer = SlowBuffer;
exports.INSPECT_MAX_BYTES = 50;
var K_MAX_LENGTH = 0x7fffffff;
exports.kMaxLength = K_MAX_LENGTH;

/**
 * If `Buffer.TYPED_ARRAY_SUPPORT`:
 *   === true    Use Uint8Array implementation (fastest)
 *   === false   Print warning and recommend using `buffer` v4.x which has an Object
 *               implementation (most compatible, even IE6)
 *
 * Browsers that support typed arrays are IE 10+, Firefox 4+, Chrome 7+, Safari 5.1+,
 * Opera 11.6+, iOS 4.2+.
 *
 * We report that the browser does not support typed arrays if the are not subclassable
 * using __proto__. Firefox 4-29 lacks support for adding new properties to `Uint8Array`
 * (See: https://bugzilla.mozilla.org/show_bug.cgi?id=695438). IE 10 lacks support
 * for __proto__ and has a buggy typed array implementation.
 */
Buffer.TYPED_ARRAY_SUPPORT = typedArraySupport();
if (!Buffer.TYPED_ARRAY_SUPPORT && typeof console !== 'undefined' && typeof console.error === 'function') {
  console.error('This browser lacks typed array (Uint8Array) support which is required by ' + '`buffer` v5.x. Use `buffer` v4.x if you require old browser support.');
}
function typedArraySupport() {
  // Can typed array instances can be augmented?
  try {
    var arr = new Uint8Array(1);
    var proto = {
      foo: function foo() {
        return 42;
      }
    };
    Object.setPrototypeOf(proto, Uint8Array.prototype);
    Object.setPrototypeOf(arr, proto);
    return arr.foo() === 42;
  } catch (e) {
    return false;
  }
}
Object.defineProperty(Buffer.prototype, 'parent', {
  enumerable: true,
  get: function get() {
    if (!Buffer.isBuffer(this)) return undefined;
    return this.buffer;
  }
});
Object.defineProperty(Buffer.prototype, 'offset', {
  enumerable: true,
  get: function get() {
    if (!Buffer.isBuffer(this)) return undefined;
    return this.byteOffset;
  }
});
function createBuffer(length) {
  if (length > K_MAX_LENGTH) {
    throw new RangeError('The value "' + length + '" is invalid for option "size"');
  }
  // Return an augmented `Uint8Array` instance
  var buf = new Uint8Array(length);
  Object.setPrototypeOf(buf, Buffer.prototype);
  return buf;
}

/**
 * The Buffer constructor returns instances of `Uint8Array` that have their
 * prototype changed to `Buffer.prototype`. Furthermore, `Buffer` is a subclass of
 * `Uint8Array`, so the returned instances will have all the node `Buffer` methods
 * and the `Uint8Array` methods. Square bracket notation works as expected -- it
 * returns a single octet.
 *
 * The `Uint8Array` prototype remains unmodified.
 */

function Buffer(arg, encodingOrOffset, length) {
  // Common case.
  if (typeof arg === 'number') {
    if (typeof encodingOrOffset === 'string') {
      throw new TypeError('The "string" argument must be of type string. Received type number');
    }
    return allocUnsafe(arg);
  }
  return from(arg, encodingOrOffset, length);
}
Buffer.poolSize = 8192; // not used by this implementation

function from(value, encodingOrOffset, length) {
  if (typeof value === 'string') {
    return fromString(value, encodingOrOffset);
  }
  if (ArrayBuffer.isView(value)) {
    return fromArrayView(value);
  }
  if (value == null) {
    throw new TypeError('The first argument must be one of type string, Buffer, ArrayBuffer, Array, ' + 'or Array-like Object. Received type ' + typeof value);
  }
  if (isInstance(value, ArrayBuffer) || value && isInstance(value.buffer, ArrayBuffer)) {
    return fromArrayBuffer(value, encodingOrOffset, length);
  }
  if (typeof SharedArrayBuffer !== 'undefined' && (isInstance(value, SharedArrayBuffer) || value && isInstance(value.buffer, SharedArrayBuffer))) {
    return fromArrayBuffer(value, encodingOrOffset, length);
  }
  if (typeof value === 'number') {
    throw new TypeError('The "value" argument must not be of type number. Received type number');
  }
  var valueOf = value.valueOf && value.valueOf();
  if (valueOf != null && valueOf !== value) {
    return Buffer.from(valueOf, encodingOrOffset, length);
  }
  var b = fromObject(value);
  if (b) return b;
  if (typeof Symbol !== 'undefined' && Symbol.toPrimitive != null && typeof value[Symbol.toPrimitive] === 'function') {
    return Buffer.from(value[Symbol.toPrimitive]('string'), encodingOrOffset, length);
  }
  throw new TypeError('The first argument must be one of type string, Buffer, ArrayBuffer, Array, ' + 'or Array-like Object. Received type ' + typeof value);
}

/**
 * Functionally equivalent to Buffer(arg, encoding) but throws a TypeError
 * if value is a number.
 * Buffer.from(str[, encoding])
 * Buffer.from(array)
 * Buffer.from(buffer)
 * Buffer.from(arrayBuffer[, byteOffset[, length]])
 **/
Buffer.from = function (value, encodingOrOffset, length) {
  return from(value, encodingOrOffset, length);
};

// Note: Change prototype *after* Buffer.from is defined to workaround Chrome bug:
// https://github.com/feross/buffer/pull/148
Object.setPrototypeOf(Buffer.prototype, Uint8Array.prototype);
Object.setPrototypeOf(Buffer, Uint8Array);
function assertSize(size) {
  if (typeof size !== 'number') {
    throw new TypeError('"size" argument must be of type number');
  } else if (size < 0) {
    throw new RangeError('The value "' + size + '" is invalid for option "size"');
  }
}
function alloc(size, fill, encoding) {
  assertSize(size);
  if (size <= 0) {
    return createBuffer(size);
  }
  if (fill !== undefined) {
    // Only pay attention to encoding if it's a string. This
    // prevents accidentally sending in a number that would
    // be interpreted as a start offset.
    return typeof encoding === 'string' ? createBuffer(size).fill(fill, encoding) : createBuffer(size).fill(fill);
  }
  return createBuffer(size);
}

/**
 * Creates a new filled Buffer instance.
 * alloc(size[, fill[, encoding]])
 **/
Buffer.alloc = function (size, fill, encoding) {
  return alloc(size, fill, encoding);
};
function allocUnsafe(size) {
  assertSize(size);
  return createBuffer(size < 0 ? 0 : checked(size) | 0);
}

/**
 * Equivalent to Buffer(num), by default creates a non-zero-filled Buffer instance.
 * */
Buffer.allocUnsafe = function (size) {
  return allocUnsafe(size);
};
/**
 * Equivalent to SlowBuffer(num), by default creates a non-zero-filled Buffer instance.
 */
Buffer.allocUnsafeSlow = function (size) {
  return allocUnsafe(size);
};
function fromString(string, encoding) {
  if (typeof encoding !== 'string' || encoding === '') {
    encoding = 'utf8';
  }
  if (!Buffer.isEncoding(encoding)) {
    throw new TypeError('Unknown encoding: ' + encoding);
  }
  var length = byteLength(string, encoding) | 0;
  var buf = createBuffer(length);
  var actual = buf.write(string, encoding);
  if (actual !== length) {
    // Writing a hex string, for example, that contains invalid characters will
    // cause everything after the first invalid character to be ignored. (e.g.
    // 'abxxcd' will be treated as 'ab')
    buf = buf.slice(0, actual);
  }
  return buf;
}
function fromArrayLike(array) {
  var length = array.length < 0 ? 0 : checked(array.length) | 0;
  var buf = createBuffer(length);
  for (var i = 0; i < length; i += 1) {
    buf[i] = array[i] & 255;
  }
  return buf;
}
function fromArrayView(arrayView) {
  if (isInstance(arrayView, Uint8Array)) {
    var copy = new Uint8Array(arrayView);
    return fromArrayBuffer(copy.buffer, copy.byteOffset, copy.byteLength);
  }
  return fromArrayLike(arrayView);
}
function fromArrayBuffer(array, byteOffset, length) {
  if (byteOffset < 0 || array.byteLength < byteOffset) {
    throw new RangeError('"offset" is outside of buffer bounds');
  }
  if (array.byteLength < byteOffset + (length || 0)) {
    throw new RangeError('"length" is outside of buffer bounds');
  }
  var buf;
  if (byteOffset === undefined && length === undefined) {
    buf = new Uint8Array(array);
  } else if (length === undefined) {
    buf = new Uint8Array(array, byteOffset);
  } else {
    buf = new Uint8Array(array, byteOffset, length);
  }

  // Return an augmented `Uint8Array` instance
  Object.setPrototypeOf(buf, Buffer.prototype);
  return buf;
}
function fromObject(obj) {
  if (Buffer.isBuffer(obj)) {
    var len = checked(obj.length) | 0;
    var buf = createBuffer(len);
    if (buf.length === 0) {
      return buf;
    }
    obj.copy(buf, 0, 0, len);
    return buf;
  }
  if (obj.length !== undefined) {
    if (typeof obj.length !== 'number' || numberIsNaN(obj.length)) {
      return createBuffer(0);
    }
    return fromArrayLike(obj);
  }
  if (obj.type === 'Buffer' && Array.isArray(obj.data)) {
    return fromArrayLike(obj.data);
  }
}
function checked(length) {
  // Note: cannot use `length < K_MAX_LENGTH` here because that fails when
  // length is NaN (which is otherwise coerced to zero.)
  if (length >= K_MAX_LENGTH) {
    throw new RangeError('Attempt to allocate Buffer larger than maximum ' + 'size: 0x' + K_MAX_LENGTH.toString(16) + ' bytes');
  }
  return length | 0;
}
function SlowBuffer(length) {
  if (+length != length) {
    // eslint-disable-line eqeqeq
    length = 0;
  }
  return Buffer.alloc(+length);
}
Buffer.isBuffer = function isBuffer(b) {
  return b != null && b._isBuffer === true && b !== Buffer.prototype; // so Buffer.isBuffer(Buffer.prototype) will be false
};
Buffer.compare = function compare(a, b) {
  if (isInstance(a, Uint8Array)) a = Buffer.from(a, a.offset, a.byteLength);
  if (isInstance(b, Uint8Array)) b = Buffer.from(b, b.offset, b.byteLength);
  if (!Buffer.isBuffer(a) || !Buffer.isBuffer(b)) {
    throw new TypeError('The "buf1", "buf2" arguments must be one of type Buffer or Uint8Array');
  }
  if (a === b) return 0;
  var x = a.length;
  var y = b.length;
  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i];
      y = b[i];
      break;
    }
  }
  if (x < y) return -1;
  if (y < x) return 1;
  return 0;
};
Buffer.isEncoding = function isEncoding(encoding) {
  switch (String(encoding).toLowerCase()) {
    case 'hex':
    case 'utf8':
    case 'utf-8':
    case 'ascii':
    case 'latin1':
    case 'binary':
    case 'base64':
    case 'ucs2':
    case 'ucs-2':
    case 'utf16le':
    case 'utf-16le':
      return true;
    default:
      return false;
  }
};
Buffer.concat = function concat(list, length) {
  if (!Array.isArray(list)) {
    throw new TypeError('"list" argument must be an Array of Buffers');
  }
  if (list.length === 0) {
    return Buffer.alloc(0);
  }
  var i;
  if (length === undefined) {
    length = 0;
    for (i = 0; i < list.length; ++i) {
      length += list[i].length;
    }
  }
  var buffer = Buffer.allocUnsafe(length);
  var pos = 0;
  for (i = 0; i < list.length; ++i) {
    var buf = list[i];
    if (isInstance(buf, Uint8Array)) {
      if (pos + buf.length > buffer.length) {
        if (!Buffer.isBuffer(buf)) buf = Buffer.from(buf);
        buf.copy(buffer, pos);
      } else {
        Uint8Array.prototype.set.call(buffer, buf, pos);
      }
    } else if (!Buffer.isBuffer(buf)) {
      throw new TypeError('"list" argument must be an Array of Buffers');
    } else {
      buf.copy(buffer, pos);
    }
    pos += buf.length;
  }
  return buffer;
};
function byteLength(string, encoding) {
  if (Buffer.isBuffer(string)) {
    return string.length;
  }
  if (ArrayBuffer.isView(string) || isInstance(string, ArrayBuffer)) {
    return string.byteLength;
  }
  if (typeof string !== 'string') {
    throw new TypeError('The "string" argument must be one of type string, Buffer, or ArrayBuffer. ' + 'Received type ' + typeof string);
  }
  var len = string.length;
  var mustMatch = arguments.length > 2 && arguments[2] === true;
  if (!mustMatch && len === 0) return 0;

  // Use a for loop to avoid recursion
  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'ascii':
      case 'latin1':
      case 'binary':
        return len;
      case 'utf8':
      case 'utf-8':
        return utf8ToBytes(string).length;
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return len * 2;
      case 'hex':
        return len >>> 1;
      case 'base64':
        return base64ToBytes(string).length;
      default:
        if (loweredCase) {
          return mustMatch ? -1 : utf8ToBytes(string).length; // assume utf8
        }
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
}
Buffer.byteLength = byteLength;
function slowToString(encoding, start, end) {
  var loweredCase = false;

  // No need to verify that "this.length <= MAX_UINT32" since it's a read-only
  // property of a typed array.

  // This behaves neither like String nor Uint8Array in that we set start/end
  // to their upper/lower bounds if the value passed is out of range.
  // undefined is handled specially as per ECMA-262 6th Edition,
  // Section 13.3.3.7 Runtime Semantics: KeyedBindingInitialization.
  if (start === undefined || start < 0) {
    start = 0;
  }
  // Return early if start > this.length. Done here to prevent potential uint32
  // coercion fail below.
  if (start > this.length) {
    return '';
  }
  if (end === undefined || end > this.length) {
    end = this.length;
  }
  if (end <= 0) {
    return '';
  }

  // Force coercion to uint32. This will also coerce falsey/NaN values to 0.
  end >>>= 0;
  start >>>= 0;
  if (end <= start) {
    return '';
  }
  if (!encoding) encoding = 'utf8';
  while (true) {
    switch (encoding) {
      case 'hex':
        return hexSlice(this, start, end);
      case 'utf8':
      case 'utf-8':
        return utf8Slice(this, start, end);
      case 'ascii':
        return asciiSlice(this, start, end);
      case 'latin1':
      case 'binary':
        return latin1Slice(this, start, end);
      case 'base64':
        return base64Slice(this, start, end);
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return utf16leSlice(this, start, end);
      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding);
        encoding = (encoding + '').toLowerCase();
        loweredCase = true;
    }
  }
}

// This property is used by `Buffer.isBuffer` (and the `is-buffer` npm package)
// to detect a Buffer instance. It's not possible to use `instanceof Buffer`
// reliably in a browserify context because there could be multiple different
// copies of the 'buffer' package in use. This method works even for Buffer
// instances that were created from another copy of the `buffer` package.
// See: https://github.com/feross/buffer/issues/154
Buffer.prototype._isBuffer = true;
function swap(b, n, m) {
  var i = b[n];
  b[n] = b[m];
  b[m] = i;
}
Buffer.prototype.swap16 = function swap16() {
  var len = this.length;
  if (len % 2 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 16-bits');
  }
  for (var i = 0; i < len; i += 2) {
    swap(this, i, i + 1);
  }
  return this;
};
Buffer.prototype.swap32 = function swap32() {
  var len = this.length;
  if (len % 4 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 32-bits');
  }
  for (var i = 0; i < len; i += 4) {
    swap(this, i, i + 3);
    swap(this, i + 1, i + 2);
  }
  return this;
};
Buffer.prototype.swap64 = function swap64() {
  var len = this.length;
  if (len % 8 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 64-bits');
  }
  for (var i = 0; i < len; i += 8) {
    swap(this, i, i + 7);
    swap(this, i + 1, i + 6);
    swap(this, i + 2, i + 5);
    swap(this, i + 3, i + 4);
  }
  return this;
};
Buffer.prototype.toString = function toString() {
  var length = this.length;
  if (length === 0) return '';
  if (arguments.length === 0) return utf8Slice(this, 0, length);
  return slowToString.apply(this, arguments);
};
Buffer.prototype.toLocaleString = Buffer.prototype.toString;
Buffer.prototype.equals = function equals(b) {
  if (!Buffer.isBuffer(b)) throw new TypeError('Argument must be a Buffer');
  if (this === b) return true;
  return Buffer.compare(this, b) === 0;
};
Buffer.prototype.inspect = function inspect() {
  var str = '';
  var max = exports.INSPECT_MAX_BYTES;
  str = this.toString('hex', 0, max).replace(/(.{2})/g, '$1 ').trim();
  if (this.length > max) str += ' ... ';
  return '<Buffer ' + str + '>';
};
if (customInspectSymbol) {
  Buffer.prototype[customInspectSymbol] = Buffer.prototype.inspect;
}
Buffer.prototype.compare = function compare(target, start, end, thisStart, thisEnd) {
  if (isInstance(target, Uint8Array)) {
    target = Buffer.from(target, target.offset, target.byteLength);
  }
  if (!Buffer.isBuffer(target)) {
    throw new TypeError('The "target" argument must be one of type Buffer or Uint8Array. ' + 'Received type ' + typeof target);
  }
  if (start === undefined) {
    start = 0;
  }
  if (end === undefined) {
    end = target ? target.length : 0;
  }
  if (thisStart === undefined) {
    thisStart = 0;
  }
  if (thisEnd === undefined) {
    thisEnd = this.length;
  }
  if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
    throw new RangeError('out of range index');
  }
  if (thisStart >= thisEnd && start >= end) {
    return 0;
  }
  if (thisStart >= thisEnd) {
    return -1;
  }
  if (start >= end) {
    return 1;
  }
  start >>>= 0;
  end >>>= 0;
  thisStart >>>= 0;
  thisEnd >>>= 0;
  if (this === target) return 0;
  var x = thisEnd - thisStart;
  var y = end - start;
  var len = Math.min(x, y);
  var thisCopy = this.slice(thisStart, thisEnd);
  var targetCopy = target.slice(start, end);
  for (var i = 0; i < len; ++i) {
    if (thisCopy[i] !== targetCopy[i]) {
      x = thisCopy[i];
      y = targetCopy[i];
      break;
    }
  }
  if (x < y) return -1;
  if (y < x) return 1;
  return 0;
};

// Finds either the first index of `val` in `buffer` at offset >= `byteOffset`,
// OR the last index of `val` in `buffer` at offset <= `byteOffset`.
//
// Arguments:
// - buffer - a Buffer to search
// - val - a string, Buffer, or number
// - byteOffset - an index into `buffer`; will be clamped to an int32
// - encoding - an optional encoding, relevant is val is a string
// - dir - true for indexOf, false for lastIndexOf
function bidirectionalIndexOf(buffer, val, byteOffset, encoding, dir) {
  // Empty buffer means no match
  if (buffer.length === 0) return -1;

  // Normalize byteOffset
  if (typeof byteOffset === 'string') {
    encoding = byteOffset;
    byteOffset = 0;
  } else if (byteOffset > 0x7fffffff) {
    byteOffset = 0x7fffffff;
  } else if (byteOffset < -0x80000000) {
    byteOffset = -0x80000000;
  }
  byteOffset = +byteOffset; // Coerce to Number.
  if (numberIsNaN(byteOffset)) {
    // byteOffset: it it's undefined, null, NaN, "foo", etc, search whole buffer
    byteOffset = dir ? 0 : buffer.length - 1;
  }

  // Normalize byteOffset: negative offsets start from the end of the buffer
  if (byteOffset < 0) byteOffset = buffer.length + byteOffset;
  if (byteOffset >= buffer.length) {
    if (dir) return -1;else byteOffset = buffer.length - 1;
  } else if (byteOffset < 0) {
    if (dir) byteOffset = 0;else return -1;
  }

  // Normalize val
  if (typeof val === 'string') {
    val = Buffer.from(val, encoding);
  }

  // Finally, search either indexOf (if dir is true) or lastIndexOf
  if (Buffer.isBuffer(val)) {
    // Special case: looking for empty string/buffer always fails
    if (val.length === 0) {
      return -1;
    }
    return arrayIndexOf(buffer, val, byteOffset, encoding, dir);
  } else if (typeof val === 'number') {
    val = val & 0xFF; // Search for a byte value [0-255]
    if (typeof Uint8Array.prototype.indexOf === 'function') {
      if (dir) {
        return Uint8Array.prototype.indexOf.call(buffer, val, byteOffset);
      } else {
        return Uint8Array.prototype.lastIndexOf.call(buffer, val, byteOffset);
      }
    }
    return arrayIndexOf(buffer, [val], byteOffset, encoding, dir);
  }
  throw new TypeError('val must be string, number or Buffer');
}
function arrayIndexOf(arr, val, byteOffset, encoding, dir) {
  var indexSize = 1;
  var arrLength = arr.length;
  var valLength = val.length;
  if (encoding !== undefined) {
    encoding = String(encoding).toLowerCase();
    if (encoding === 'ucs2' || encoding === 'ucs-2' || encoding === 'utf16le' || encoding === 'utf-16le') {
      if (arr.length < 2 || val.length < 2) {
        return -1;
      }
      indexSize = 2;
      arrLength /= 2;
      valLength /= 2;
      byteOffset /= 2;
    }
  }
  function read(buf, i) {
    if (indexSize === 1) {
      return buf[i];
    } else {
      return buf.readUInt16BE(i * indexSize);
    }
  }
  var i;
  if (dir) {
    var foundIndex = -1;
    for (i = byteOffset; i < arrLength; i++) {
      if (read(arr, i) === read(val, foundIndex === -1 ? 0 : i - foundIndex)) {
        if (foundIndex === -1) foundIndex = i;
        if (i - foundIndex + 1 === valLength) return foundIndex * indexSize;
      } else {
        if (foundIndex !== -1) i -= i - foundIndex;
        foundIndex = -1;
      }
    }
  } else {
    if (byteOffset + valLength > arrLength) byteOffset = arrLength - valLength;
    for (i = byteOffset; i >= 0; i--) {
      var found = true;
      for (var j = 0; j < valLength; j++) {
        if (read(arr, i + j) !== read(val, j)) {
          found = false;
          break;
        }
      }
      if (found) return i;
    }
  }
  return -1;
}
Buffer.prototype.includes = function includes(val, byteOffset, encoding) {
  return this.indexOf(val, byteOffset, encoding) !== -1;
};
Buffer.prototype.indexOf = function indexOf(val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, true);
};
Buffer.prototype.lastIndexOf = function lastIndexOf(val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, false);
};
function hexWrite(buf, string, offset, length) {
  offset = Number(offset) || 0;
  var remaining = buf.length - offset;
  if (!length) {
    length = remaining;
  } else {
    length = Number(length);
    if (length > remaining) {
      length = remaining;
    }
  }
  var strLen = string.length;
  if (length > strLen / 2) {
    length = strLen / 2;
  }
  var i;
  for (i = 0; i < length; ++i) {
    var parsed = parseInt(string.substr(i * 2, 2), 16);
    if (numberIsNaN(parsed)) return i;
    buf[offset + i] = parsed;
  }
  return i;
}
function utf8Write(buf, string, offset, length) {
  return blitBuffer(utf8ToBytes(string, buf.length - offset), buf, offset, length);
}
function asciiWrite(buf, string, offset, length) {
  return blitBuffer(asciiToBytes(string), buf, offset, length);
}
function base64Write(buf, string, offset, length) {
  return blitBuffer(base64ToBytes(string), buf, offset, length);
}
function ucs2Write(buf, string, offset, length) {
  return blitBuffer(utf16leToBytes(string, buf.length - offset), buf, offset, length);
}
Buffer.prototype.write = function write(string, offset, length, encoding) {
  // Buffer#write(string)
  if (offset === undefined) {
    encoding = 'utf8';
    length = this.length;
    offset = 0;
    // Buffer#write(string, encoding)
  } else if (length === undefined && typeof offset === 'string') {
    encoding = offset;
    length = this.length;
    offset = 0;
    // Buffer#write(string, offset[, length][, encoding])
  } else if (isFinite(offset)) {
    offset = offset >>> 0;
    if (isFinite(length)) {
      length = length >>> 0;
      if (encoding === undefined) encoding = 'utf8';
    } else {
      encoding = length;
      length = undefined;
    }
  } else {
    throw new Error('Buffer.write(string, encoding, offset[, length]) is no longer supported');
  }
  var remaining = this.length - offset;
  if (length === undefined || length > remaining) length = remaining;
  if (string.length > 0 && (length < 0 || offset < 0) || offset > this.length) {
    throw new RangeError('Attempt to write outside buffer bounds');
  }
  if (!encoding) encoding = 'utf8';
  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'hex':
        return hexWrite(this, string, offset, length);
      case 'utf8':
      case 'utf-8':
        return utf8Write(this, string, offset, length);
      case 'ascii':
      case 'latin1':
      case 'binary':
        return asciiWrite(this, string, offset, length);
      case 'base64':
        // Warning: maxLength not taken into account in base64Write
        return base64Write(this, string, offset, length);
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return ucs2Write(this, string, offset, length);
      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding);
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
};
Buffer.prototype.toJSON = function toJSON() {
  return {
    type: 'Buffer',
    data: Array.prototype.slice.call(this._arr || this, 0)
  };
};
function base64Slice(buf, start, end) {
  if (start === 0 && end === buf.length) {
    return base64.fromByteArray(buf);
  } else {
    return base64.fromByteArray(buf.slice(start, end));
  }
}
function utf8Slice(buf, start, end) {
  end = Math.min(buf.length, end);
  var res = [];
  var i = start;
  while (i < end) {
    var firstByte = buf[i];
    var codePoint = null;
    var bytesPerSequence = firstByte > 0xEF ? 4 : firstByte > 0xDF ? 3 : firstByte > 0xBF ? 2 : 1;
    if (i + bytesPerSequence <= end) {
      var secondByte = void 0,
        thirdByte = void 0,
        fourthByte = void 0,
        tempCodePoint = void 0;
      switch (bytesPerSequence) {
        case 1:
          if (firstByte < 0x80) {
            codePoint = firstByte;
          }
          break;
        case 2:
          secondByte = buf[i + 1];
          if ((secondByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0x1F) << 0x6 | secondByte & 0x3F;
            if (tempCodePoint > 0x7F) {
              codePoint = tempCodePoint;
            }
          }
          break;
        case 3:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0xC | (secondByte & 0x3F) << 0x6 | thirdByte & 0x3F;
            if (tempCodePoint > 0x7FF && (tempCodePoint < 0xD800 || tempCodePoint > 0xDFFF)) {
              codePoint = tempCodePoint;
            }
          }
          break;
        case 4:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          fourthByte = buf[i + 3];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80 && (fourthByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0x12 | (secondByte & 0x3F) << 0xC | (thirdByte & 0x3F) << 0x6 | fourthByte & 0x3F;
            if (tempCodePoint > 0xFFFF && tempCodePoint < 0x110000) {
              codePoint = tempCodePoint;
            }
          }
      }
    }
    if (codePoint === null) {
      // we did not generate a valid codePoint so insert a
      // replacement char (U+FFFD) and advance only 1 byte
      codePoint = 0xFFFD;
      bytesPerSequence = 1;
    } else if (codePoint > 0xFFFF) {
      // encode to utf16 (surrogate pair dance)
      codePoint -= 0x10000;
      res.push(codePoint >>> 10 & 0x3FF | 0xD800);
      codePoint = 0xDC00 | codePoint & 0x3FF;
    }
    res.push(codePoint);
    i += bytesPerSequence;
  }
  return decodeCodePointsArray(res);
}

// Based on http://stackoverflow.com/a/22747272/680742, the browser with
// the lowest limit is Chrome, with 0x10000 args.
// We go 1 magnitude less, for safety
var MAX_ARGUMENTS_LENGTH = 0x1000;
function decodeCodePointsArray(codePoints) {
  var len = codePoints.length;
  if (len <= MAX_ARGUMENTS_LENGTH) {
    return String.fromCharCode.apply(String, codePoints); // avoid extra slice()
  }

  // Decode in chunks to avoid "call stack size exceeded".
  var res = '';
  var i = 0;
  while (i < len) {
    res += String.fromCharCode.apply(String, codePoints.slice(i, i += MAX_ARGUMENTS_LENGTH));
  }
  return res;
}
function asciiSlice(buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);
  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i] & 0x7F);
  }
  return ret;
}
function latin1Slice(buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);
  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i]);
  }
  return ret;
}
function hexSlice(buf, start, end) {
  var len = buf.length;
  if (!start || start < 0) start = 0;
  if (!end || end < 0 || end > len) end = len;
  var out = '';
  for (var i = start; i < end; ++i) {
    out += hexSliceLookupTable[buf[i]];
  }
  return out;
}
function utf16leSlice(buf, start, end) {
  var bytes = buf.slice(start, end);
  var res = '';
  // If bytes.length is odd, the last 8 bits must be ignored (same as node.js)
  for (var i = 0; i < bytes.length - 1; i += 2) {
    res += String.fromCharCode(bytes[i] + bytes[i + 1] * 256);
  }
  return res;
}
Buffer.prototype.slice = function slice(start, end) {
  var len = this.length;
  start = ~~start;
  end = end === undefined ? len : ~~end;
  if (start < 0) {
    start += len;
    if (start < 0) start = 0;
  } else if (start > len) {
    start = len;
  }
  if (end < 0) {
    end += len;
    if (end < 0) end = 0;
  } else if (end > len) {
    end = len;
  }
  if (end < start) end = start;
  var newBuf = this.subarray(start, end);
  // Return an augmented `Uint8Array` instance
  Object.setPrototypeOf(newBuf, Buffer.prototype);
  return newBuf;
};

/*
 * Need to make sure that buffer isn't trying to write out of bounds.
 */
function checkOffset(offset, ext, length) {
  if (offset % 1 !== 0 || offset < 0) throw new RangeError('offset is not uint');
  if (offset + ext > length) throw new RangeError('Trying to access beyond buffer length');
}
Buffer.prototype.readUintLE = Buffer.prototype.readUIntLE = function readUIntLE(offset, byteLength, noAssert) {
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);
  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }
  return val;
};
Buffer.prototype.readUintBE = Buffer.prototype.readUIntBE = function readUIntBE(offset, byteLength, noAssert) {
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) {
    checkOffset(offset, byteLength, this.length);
  }
  var val = this[offset + --byteLength];
  var mul = 1;
  while (byteLength > 0 && (mul *= 0x100)) {
    val += this[offset + --byteLength] * mul;
  }
  return val;
};
Buffer.prototype.readUint8 = Buffer.prototype.readUInt8 = function readUInt8(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 1, this.length);
  return this[offset];
};
Buffer.prototype.readUint16LE = Buffer.prototype.readUInt16LE = function readUInt16LE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 2, this.length);
  return this[offset] | this[offset + 1] << 8;
};
Buffer.prototype.readUint16BE = Buffer.prototype.readUInt16BE = function readUInt16BE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 2, this.length);
  return this[offset] << 8 | this[offset + 1];
};
Buffer.prototype.readUint32LE = Buffer.prototype.readUInt32LE = function readUInt32LE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return (this[offset] | this[offset + 1] << 8 | this[offset + 2] << 16) + this[offset + 3] * 0x1000000;
};
Buffer.prototype.readUint32BE = Buffer.prototype.readUInt32BE = function readUInt32BE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return this[offset] * 0x1000000 + (this[offset + 1] << 16 | this[offset + 2] << 8 | this[offset + 3]);
};
Buffer.prototype.readBigUInt64LE = defineBigIntMethod(function readBigUInt64LE(offset) {
  offset = offset >>> 0;
  validateNumber(offset, 'offset');
  var first = this[offset];
  var last = this[offset + 7];
  if (first === undefined || last === undefined) {
    boundsError(offset, this.length - 8);
  }
  var lo = first + this[++offset] * Math.pow(2, 8) + this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 24);
  var hi = this[++offset] + this[++offset] * Math.pow(2, 8) + this[++offset] * Math.pow(2, 16) + last * Math.pow(2, 24);
  return BigInt(lo) + (BigInt(hi) << BigInt(32));
});
Buffer.prototype.readBigUInt64BE = defineBigIntMethod(function readBigUInt64BE(offset) {
  offset = offset >>> 0;
  validateNumber(offset, 'offset');
  var first = this[offset];
  var last = this[offset + 7];
  if (first === undefined || last === undefined) {
    boundsError(offset, this.length - 8);
  }
  var hi = first * Math.pow(2, 24) + this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 8) + this[++offset];
  var lo = this[++offset] * Math.pow(2, 24) + this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 8) + last;
  return (BigInt(hi) << BigInt(32)) + BigInt(lo);
});
Buffer.prototype.readIntLE = function readIntLE(offset, byteLength, noAssert) {
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);
  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }
  mul *= 0x80;
  if (val >= mul) val -= Math.pow(2, 8 * byteLength);
  return val;
};
Buffer.prototype.readIntBE = function readIntBE(offset, byteLength, noAssert) {
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);
  var i = byteLength;
  var mul = 1;
  var val = this[offset + --i];
  while (i > 0 && (mul *= 0x100)) {
    val += this[offset + --i] * mul;
  }
  mul *= 0x80;
  if (val >= mul) val -= Math.pow(2, 8 * byteLength);
  return val;
};
Buffer.prototype.readInt8 = function readInt8(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 1, this.length);
  if (!(this[offset] & 0x80)) return this[offset];
  return (0xff - this[offset] + 1) * -1;
};
Buffer.prototype.readInt16LE = function readInt16LE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset] | this[offset + 1] << 8;
  return val & 0x8000 ? val | 0xFFFF0000 : val;
};
Buffer.prototype.readInt16BE = function readInt16BE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset + 1] | this[offset] << 8;
  return val & 0x8000 ? val | 0xFFFF0000 : val;
};
Buffer.prototype.readInt32LE = function readInt32LE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return this[offset] | this[offset + 1] << 8 | this[offset + 2] << 16 | this[offset + 3] << 24;
};
Buffer.prototype.readInt32BE = function readInt32BE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return this[offset] << 24 | this[offset + 1] << 16 | this[offset + 2] << 8 | this[offset + 3];
};
Buffer.prototype.readBigInt64LE = defineBigIntMethod(function readBigInt64LE(offset) {
  offset = offset >>> 0;
  validateNumber(offset, 'offset');
  var first = this[offset];
  var last = this[offset + 7];
  if (first === undefined || last === undefined) {
    boundsError(offset, this.length - 8);
  }
  var val = this[offset + 4] + this[offset + 5] * Math.pow(2, 8) + this[offset + 6] * Math.pow(2, 16) + (last << 24); // Overflow

  return (BigInt(val) << BigInt(32)) + BigInt(first + this[++offset] * Math.pow(2, 8) + this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 24));
});
Buffer.prototype.readBigInt64BE = defineBigIntMethod(function readBigInt64BE(offset) {
  offset = offset >>> 0;
  validateNumber(offset, 'offset');
  var first = this[offset];
  var last = this[offset + 7];
  if (first === undefined || last === undefined) {
    boundsError(offset, this.length - 8);
  }
  var val = (first << 24) +
  // Overflow
  this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 8) + this[++offset];
  return (BigInt(val) << BigInt(32)) + BigInt(this[++offset] * Math.pow(2, 24) + this[++offset] * Math.pow(2, 16) + this[++offset] * Math.pow(2, 8) + last);
});
Buffer.prototype.readFloatLE = function readFloatLE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return ieee754.read(this, offset, true, 23, 4);
};
Buffer.prototype.readFloatBE = function readFloatBE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 4, this.length);
  return ieee754.read(this, offset, false, 23, 4);
};
Buffer.prototype.readDoubleLE = function readDoubleLE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 8, this.length);
  return ieee754.read(this, offset, true, 52, 8);
};
Buffer.prototype.readDoubleBE = function readDoubleBE(offset, noAssert) {
  offset = offset >>> 0;
  if (!noAssert) checkOffset(offset, 8, this.length);
  return ieee754.read(this, offset, false, 52, 8);
};
function checkInt(buf, value, offset, ext, max, min) {
  if (!Buffer.isBuffer(buf)) throw new TypeError('"buffer" argument must be a Buffer instance');
  if (value > max || value < min) throw new RangeError('"value" argument is out of bounds');
  if (offset + ext > buf.length) throw new RangeError('Index out of range');
}
Buffer.prototype.writeUintLE = Buffer.prototype.writeUIntLE = function writeUIntLE(value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }
  var mul = 1;
  var i = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    this[offset + i] = value / mul & 0xFF;
  }
  return offset + byteLength;
};
Buffer.prototype.writeUintBE = Buffer.prototype.writeUIntBE = function writeUIntBE(value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset >>> 0;
  byteLength = byteLength >>> 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }
  var i = byteLength - 1;
  var mul = 1;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    this[offset + i] = value / mul & 0xFF;
  }
  return offset + byteLength;
};
Buffer.prototype.writeUint8 = Buffer.prototype.writeUInt8 = function writeUInt8(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0xff, 0);
  this[offset] = value & 0xff;
  return offset + 1;
};
Buffer.prototype.writeUint16LE = Buffer.prototype.writeUInt16LE = function writeUInt16LE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  this[offset] = value & 0xff;
  this[offset + 1] = value >>> 8;
  return offset + 2;
};
Buffer.prototype.writeUint16BE = Buffer.prototype.writeUInt16BE = function writeUInt16BE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  this[offset] = value >>> 8;
  this[offset + 1] = value & 0xff;
  return offset + 2;
};
Buffer.prototype.writeUint32LE = Buffer.prototype.writeUInt32LE = function writeUInt32LE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  this[offset + 3] = value >>> 24;
  this[offset + 2] = value >>> 16;
  this[offset + 1] = value >>> 8;
  this[offset] = value & 0xff;
  return offset + 4;
};
Buffer.prototype.writeUint32BE = Buffer.prototype.writeUInt32BE = function writeUInt32BE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  this[offset] = value >>> 24;
  this[offset + 1] = value >>> 16;
  this[offset + 2] = value >>> 8;
  this[offset + 3] = value & 0xff;
  return offset + 4;
};
function wrtBigUInt64LE(buf, value, offset, min, max) {
  checkIntBI(value, min, max, buf, offset, 7);
  var lo = Number(value & BigInt(0xffffffff));
  buf[offset++] = lo;
  lo = lo >> 8;
  buf[offset++] = lo;
  lo = lo >> 8;
  buf[offset++] = lo;
  lo = lo >> 8;
  buf[offset++] = lo;
  var hi = Number(value >> BigInt(32) & BigInt(0xffffffff));
  buf[offset++] = hi;
  hi = hi >> 8;
  buf[offset++] = hi;
  hi = hi >> 8;
  buf[offset++] = hi;
  hi = hi >> 8;
  buf[offset++] = hi;
  return offset;
}
function wrtBigUInt64BE(buf, value, offset, min, max) {
  checkIntBI(value, min, max, buf, offset, 7);
  var lo = Number(value & BigInt(0xffffffff));
  buf[offset + 7] = lo;
  lo = lo >> 8;
  buf[offset + 6] = lo;
  lo = lo >> 8;
  buf[offset + 5] = lo;
  lo = lo >> 8;
  buf[offset + 4] = lo;
  var hi = Number(value >> BigInt(32) & BigInt(0xffffffff));
  buf[offset + 3] = hi;
  hi = hi >> 8;
  buf[offset + 2] = hi;
  hi = hi >> 8;
  buf[offset + 1] = hi;
  hi = hi >> 8;
  buf[offset] = hi;
  return offset + 8;
}
Buffer.prototype.writeBigUInt64LE = defineBigIntMethod(function writeBigUInt64LE(value, offset) {
  if (offset === void 0) {
    offset = 0;
  }
  return wrtBigUInt64LE(this, value, offset, BigInt(0), BigInt('0xffffffffffffffff'));
});
Buffer.prototype.writeBigUInt64BE = defineBigIntMethod(function writeBigUInt64BE(value, offset) {
  if (offset === void 0) {
    offset = 0;
  }
  return wrtBigUInt64BE(this, value, offset, BigInt(0), BigInt('0xffffffffffffffff'));
});
Buffer.prototype.writeIntLE = function writeIntLE(value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);
    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }
  var i = 0;
  var mul = 1;
  var sub = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i - 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = (value / mul >> 0) - sub & 0xFF;
  }
  return offset + byteLength;
};
Buffer.prototype.writeIntBE = function writeIntBE(value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);
    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }
  var i = byteLength - 1;
  var mul = 1;
  var sub = 0;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i + 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = (value / mul >> 0) - sub & 0xFF;
  }
  return offset + byteLength;
};
Buffer.prototype.writeInt8 = function writeInt8(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0x7f, -0x80);
  if (value < 0) value = 0xff + value + 1;
  this[offset] = value & 0xff;
  return offset + 1;
};
Buffer.prototype.writeInt16LE = function writeInt16LE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  this[offset] = value & 0xff;
  this[offset + 1] = value >>> 8;
  return offset + 2;
};
Buffer.prototype.writeInt16BE = function writeInt16BE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  this[offset] = value >>> 8;
  this[offset + 1] = value & 0xff;
  return offset + 2;
};
Buffer.prototype.writeInt32LE = function writeInt32LE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  this[offset] = value & 0xff;
  this[offset + 1] = value >>> 8;
  this[offset + 2] = value >>> 16;
  this[offset + 3] = value >>> 24;
  return offset + 4;
};
Buffer.prototype.writeInt32BE = function writeInt32BE(value, offset, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  if (value < 0) value = 0xffffffff + value + 1;
  this[offset] = value >>> 24;
  this[offset + 1] = value >>> 16;
  this[offset + 2] = value >>> 8;
  this[offset + 3] = value & 0xff;
  return offset + 4;
};
Buffer.prototype.writeBigInt64LE = defineBigIntMethod(function writeBigInt64LE(value, offset) {
  if (offset === void 0) {
    offset = 0;
  }
  return wrtBigUInt64LE(this, value, offset, -BigInt('0x8000000000000000'), BigInt('0x7fffffffffffffff'));
});
Buffer.prototype.writeBigInt64BE = defineBigIntMethod(function writeBigInt64BE(value, offset) {
  if (offset === void 0) {
    offset = 0;
  }
  return wrtBigUInt64BE(this, value, offset, -BigInt('0x8000000000000000'), BigInt('0x7fffffffffffffff'));
});
function checkIEEE754(buf, value, offset, ext, max, min) {
  if (offset + ext > buf.length) throw new RangeError('Index out of range');
  if (offset < 0) throw new RangeError('Index out of range');
}
function writeFloat(buf, value, offset, littleEndian, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 4, 3.4028234663852886e+38, -3.4028234663852886e+38);
  }
  ieee754.write(buf, value, offset, littleEndian, 23, 4);
  return offset + 4;
}
Buffer.prototype.writeFloatLE = function writeFloatLE(value, offset, noAssert) {
  return writeFloat(this, value, offset, true, noAssert);
};
Buffer.prototype.writeFloatBE = function writeFloatBE(value, offset, noAssert) {
  return writeFloat(this, value, offset, false, noAssert);
};
function writeDouble(buf, value, offset, littleEndian, noAssert) {
  value = +value;
  offset = offset >>> 0;
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 8, 1.7976931348623157E+308, -1.7976931348623157E+308);
  }
  ieee754.write(buf, value, offset, littleEndian, 52, 8);
  return offset + 8;
}
Buffer.prototype.writeDoubleLE = function writeDoubleLE(value, offset, noAssert) {
  return writeDouble(this, value, offset, true, noAssert);
};
Buffer.prototype.writeDoubleBE = function writeDoubleBE(value, offset, noAssert) {
  return writeDouble(this, value, offset, false, noAssert);
};

// copy(targetBuffer, targetStart=0, sourceStart=0, sourceEnd=buffer.length)
Buffer.prototype.copy = function copy(target, targetStart, start, end) {
  if (!Buffer.isBuffer(target)) throw new TypeError('argument should be a Buffer');
  if (!start) start = 0;
  if (!end && end !== 0) end = this.length;
  if (targetStart >= target.length) targetStart = target.length;
  if (!targetStart) targetStart = 0;
  if (end > 0 && end < start) end = start;

  // Copy 0 bytes; we're done
  if (end === start) return 0;
  if (target.length === 0 || this.length === 0) return 0;

  // Fatal error conditions
  if (targetStart < 0) {
    throw new RangeError('targetStart out of bounds');
  }
  if (start < 0 || start >= this.length) throw new RangeError('Index out of range');
  if (end < 0) throw new RangeError('sourceEnd out of bounds');

  // Are we oob?
  if (end > this.length) end = this.length;
  if (target.length - targetStart < end - start) {
    end = target.length - targetStart + start;
  }
  var len = end - start;
  if (this === target && typeof Uint8Array.prototype.copyWithin === 'function') {
    // Use built-in when available, missing from IE11
    this.copyWithin(targetStart, start, end);
  } else {
    Uint8Array.prototype.set.call(target, this.subarray(start, end), targetStart);
  }
  return len;
};

// Usage:
//    buffer.fill(number[, offset[, end]])
//    buffer.fill(buffer[, offset[, end]])
//    buffer.fill(string[, offset[, end]][, encoding])
Buffer.prototype.fill = function fill(val, start, end, encoding) {
  // Handle string cases:
  if (typeof val === 'string') {
    if (typeof start === 'string') {
      encoding = start;
      start = 0;
      end = this.length;
    } else if (typeof end === 'string') {
      encoding = end;
      end = this.length;
    }
    if (encoding !== undefined && typeof encoding !== 'string') {
      throw new TypeError('encoding must be a string');
    }
    if (typeof encoding === 'string' && !Buffer.isEncoding(encoding)) {
      throw new TypeError('Unknown encoding: ' + encoding);
    }
    if (val.length === 1) {
      var code = val.charCodeAt(0);
      if (encoding === 'utf8' && code < 128 || encoding === 'latin1') {
        // Fast path: If `val` fits into a single byte, use that numeric value.
        val = code;
      }
    }
  } else if (typeof val === 'number') {
    val = val & 255;
  } else if (typeof val === 'boolean') {
    val = Number(val);
  }

  // Invalid ranges are not set to a default, so can range check early.
  if (start < 0 || this.length < start || this.length < end) {
    throw new RangeError('Out of range index');
  }
  if (end <= start) {
    return this;
  }
  start = start >>> 0;
  end = end === undefined ? this.length : end >>> 0;
  if (!val) val = 0;
  var i;
  if (typeof val === 'number') {
    for (i = start; i < end; ++i) {
      this[i] = val;
    }
  } else {
    var bytes = Buffer.isBuffer(val) ? val : Buffer.from(val, encoding);
    var len = bytes.length;
    if (len === 0) {
      throw new TypeError('The value "' + val + '" is invalid for argument "value"');
    }
    for (i = 0; i < end - start; ++i) {
      this[i + start] = bytes[i % len];
    }
  }
  return this;
};

// CUSTOM ERRORS
// =============

// Simplified versions from Node, changed for Buffer-only usage
var errors = {};
function E(sym, getMessage, Base) {
  errors[sym] = /*#__PURE__*/function (_Base) {
    function NodeError() {
      var _this;
      _this = _Base.call(this) || this;
      Object.defineProperty(_this, 'message', {
        value: getMessage.apply(_this, arguments),
        writable: true,
        configurable: true
      });

      // Add the error code to the name to include it in the stack trace.
      _this.name = _this.name + " [" + sym + "]";
      // Access the stack to generate the error message including the error code
      // from the name.
      _this.stack; // eslint-disable-line no-unused-expressions
      // Reset the name to the actual name.
      delete _this.name;
      return _this;
    }
    _inheritsLoose(NodeError, _Base);
    var _proto = NodeError.prototype;
    _proto.toString = function toString() {
      return this.name + " [" + sym + "]: " + this.message;
    };
    return _createClass(NodeError, [{
      key: "code",
      get: function get() {
        return sym;
      },
      set: function set(value) {
        Object.defineProperty(this, 'code', {
          configurable: true,
          enumerable: true,
          value: value,
          writable: true
        });
      }
    }]);
  }(Base);
}
E('ERR_BUFFER_OUT_OF_BOUNDS', function (name) {
  if (name) {
    return name + " is outside of buffer bounds";
  }
  return 'Attempt to access memory outside buffer bounds';
}, RangeError);
E('ERR_INVALID_ARG_TYPE', function (name, actual) {
  return "The \"" + name + "\" argument must be of type number. Received type " + typeof actual;
}, TypeError);
E('ERR_OUT_OF_RANGE', function (str, range, input) {
  var msg = "The value of \"" + str + "\" is out of range.";
  var received = input;
  if (Number.isInteger(input) && Math.abs(input) > Math.pow(2, 32)) {
    received = addNumericalSeparator(String(input));
  } else if (typeof input === 'bigint') {
    received = String(input);
    if (input > Math.pow(BigInt(2), BigInt(32)) || input < -Math.pow(BigInt(2), BigInt(32))) {
      received = addNumericalSeparator(received);
    }
    received += 'n';
  }
  msg += " It must be " + range + ". Received " + received;
  return msg;
}, RangeError);
function addNumericalSeparator(val) {
  var res = '';
  var i = val.length;
  var start = val[0] === '-' ? 1 : 0;
  for (; i >= start + 4; i -= 3) {
    res = "_" + val.slice(i - 3, i) + res;
  }
  return "" + val.slice(0, i) + res;
}

// CHECK FUNCTIONS
// ===============

function checkBounds(buf, offset, byteLength) {
  validateNumber(offset, 'offset');
  if (buf[offset] === undefined || buf[offset + byteLength] === undefined) {
    boundsError(offset, buf.length - (byteLength + 1));
  }
}
function checkIntBI(value, min, max, buf, offset, byteLength) {
  if (value > max || value < min) {
    var n = typeof min === 'bigint' ? 'n' : '';
    var range;
    if (byteLength > 3) {
      if (min === 0 || min === BigInt(0)) {
        range = ">= 0" + n + " and < 2" + n + " ** " + (byteLength + 1) * 8 + n;
      } else {
        range = ">= -(2" + n + " ** " + ((byteLength + 1) * 8 - 1) + n + ") and < 2 ** " + ("" + ((byteLength + 1) * 8 - 1) + n);
      }
    } else {
      range = ">= " + min + n + " and <= " + max + n;
    }
    throw new errors.ERR_OUT_OF_RANGE('value', range, value);
  }
  checkBounds(buf, offset, byteLength);
}
function validateNumber(value, name) {
  if (typeof value !== 'number') {
    throw new errors.ERR_INVALID_ARG_TYPE(name, 'number', value);
  }
}
function boundsError(value, length, type) {
  if (Math.floor(value) !== value) {
    validateNumber(value, type);
    throw new errors.ERR_OUT_OF_RANGE(type || 'offset', 'an integer', value);
  }
  if (length < 0) {
    throw new errors.ERR_BUFFER_OUT_OF_BOUNDS();
  }
  throw new errors.ERR_OUT_OF_RANGE(type || 'offset', ">= " + (type ? 1 : 0) + " and <= " + length, value);
}

// HELPER FUNCTIONS
// ================

var INVALID_BASE64_RE = /[^+/0-9A-Za-z-_]/g;
function base64clean(str) {
  // Node takes equal signs as end of the Base64 encoding
  str = str.split('=')[0];
  // Node strips out invalid characters like \n and \t from the string, base64-js does not
  str = str.trim().replace(INVALID_BASE64_RE, '');
  // Node converts strings with length < 2 to ''
  if (str.length < 2) return '';
  // Node allows for non-padded base64 strings (missing trailing ===), base64-js does not
  while (str.length % 4 !== 0) {
    str = str + '=';
  }
  return str;
}
function utf8ToBytes(string, units) {
  units = units || Infinity;
  var codePoint;
  var length = string.length;
  var leadSurrogate = null;
  var bytes = [];
  for (var i = 0; i < length; ++i) {
    codePoint = string.charCodeAt(i);

    // is surrogate component
    if (codePoint > 0xD7FF && codePoint < 0xE000) {
      // last char was a lead
      if (!leadSurrogate) {
        // no lead yet
        if (codePoint > 0xDBFF) {
          // unexpected trail
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue;
        } else if (i + 1 === length) {
          // unpaired lead
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue;
        }

        // valid lead
        leadSurrogate = codePoint;
        continue;
      }

      // 2 leads in a row
      if (codePoint < 0xDC00) {
        if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
        leadSurrogate = codePoint;
        continue;
      }

      // valid surrogate pair
      codePoint = (leadSurrogate - 0xD800 << 10 | codePoint - 0xDC00) + 0x10000;
    } else if (leadSurrogate) {
      // valid bmp char, but last char was a lead
      if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
    }
    leadSurrogate = null;

    // encode utf8
    if (codePoint < 0x80) {
      if ((units -= 1) < 0) break;
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      if ((units -= 2) < 0) break;
      bytes.push(codePoint >> 0x6 | 0xC0, codePoint & 0x3F | 0x80);
    } else if (codePoint < 0x10000) {
      if ((units -= 3) < 0) break;
      bytes.push(codePoint >> 0xC | 0xE0, codePoint >> 0x6 & 0x3F | 0x80, codePoint & 0x3F | 0x80);
    } else if (codePoint < 0x110000) {
      if ((units -= 4) < 0) break;
      bytes.push(codePoint >> 0x12 | 0xF0, codePoint >> 0xC & 0x3F | 0x80, codePoint >> 0x6 & 0x3F | 0x80, codePoint & 0x3F | 0x80);
    } else {
      throw new Error('Invalid code point');
    }
  }
  return bytes;
}
function asciiToBytes(str) {
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    // Node's code seems to be doing this and not & 0x7F..
    byteArray.push(str.charCodeAt(i) & 0xFF);
  }
  return byteArray;
}
function utf16leToBytes(str, units) {
  var c, hi, lo;
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    if ((units -= 2) < 0) break;
    c = str.charCodeAt(i);
    hi = c >> 8;
    lo = c % 256;
    byteArray.push(lo);
    byteArray.push(hi);
  }
  return byteArray;
}
function base64ToBytes(str) {
  return base64.toByteArray(base64clean(str));
}
function blitBuffer(src, dst, offset, length) {
  var i;
  for (i = 0; i < length; ++i) {
    if (i + offset >= dst.length || i >= src.length) break;
    dst[i + offset] = src[i];
  }
  return i;
}

// ArrayBuffer or Uint8Array objects from other contexts (i.e. iframes) do not pass
// the `instanceof` check but they should be treated as of that type.
// See: https://github.com/feross/buffer/issues/166
function isInstance(obj, type) {
  return obj instanceof type || obj != null && obj.constructor != null && obj.constructor.name != null && obj.constructor.name === type.name;
}
function numberIsNaN(obj) {
  // For IE11 support
  return obj !== obj; // eslint-disable-line no-self-compare
}

// Create lookup table for `toString('hex')`
// See: https://github.com/feross/buffer/issues/219
var hexSliceLookupTable = function () {
  var alphabet = '0123456789abcdef';
  var table = new Array(256);
  for (var i = 0; i < 16; ++i) {
    var i16 = i * 16;
    for (var j = 0; j < 16; ++j) {
      table[i16 + j] = alphabet[i] + alphabet[j];
    }
  }
  return table;
}();

// Return not function with Error if BigInt not supported
function defineBigIntMethod(fn) {
  return typeof BigInt === 'undefined' ? BufferBigIntNotDefined : fn;
}
function BufferBigIntNotDefined() {
  throw new Error('BigInt not supported');
}

/***/ }),

/***/ 30450:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(8953);
__webpack_require__(14032);
// eslint-disable-next-line es/no-typed-arrays -- safe
module.exports = typeof ArrayBuffer != 'undefined' && typeof DataView != 'undefined';

/***/ }),

/***/ 59754:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var NATIVE_ARRAY_BUFFER = __webpack_require__(30450);
var DESCRIPTORS = __webpack_require__(15567);
var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);
var hasOwn = __webpack_require__(20340);
var classof = __webpack_require__(52564);
var tryToString = __webpack_require__(68664);
var createNonEnumerableProperty = __webpack_require__(48914);
var redefine = __webpack_require__(13711);
var defineProperty = (__webpack_require__(95892).f);
var isPrototypeOf = __webpack_require__(70176);
var getPrototypeOf = __webpack_require__(69548);
var setPrototypeOf = __webpack_require__(3840);
var wellKnownSymbol = __webpack_require__(38688);
var uid = __webpack_require__(46859);
var Int8Array = global.Int8Array;
var Int8ArrayPrototype = Int8Array && Int8Array.prototype;
var Uint8ClampedArray = global.Uint8ClampedArray;
var Uint8ClampedArrayPrototype = Uint8ClampedArray && Uint8ClampedArray.prototype;
var TypedArray = Int8Array && getPrototypeOf(Int8Array);
var TypedArrayPrototype = Int8ArrayPrototype && getPrototypeOf(Int8ArrayPrototype);
var ObjectPrototype = Object.prototype;
var TypeError = global.TypeError;
var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var TYPED_ARRAY_TAG = uid('TYPED_ARRAY_TAG');
var TYPED_ARRAY_CONSTRUCTOR = uid('TYPED_ARRAY_CONSTRUCTOR');
// Fixing native typed arrays in Opera Presto crashes the browser, see #595
var NATIVE_ARRAY_BUFFER_VIEWS = NATIVE_ARRAY_BUFFER && !!setPrototypeOf && classof(global.opera) !== 'Opera';
var TYPED_ARRAY_TAG_REQIRED = false;
var NAME, Constructor, Prototype;
var TypedArrayConstructorsList = {
  Int8Array: 1,
  Uint8Array: 1,
  Uint8ClampedArray: 1,
  Int16Array: 2,
  Uint16Array: 2,
  Int32Array: 4,
  Uint32Array: 4,
  Float32Array: 4,
  Float64Array: 8
};
var BigIntArrayConstructorsList = {
  BigInt64Array: 8,
  BigUint64Array: 8
};
var isView = function isView(it) {
  if (!isObject(it)) return false;
  var klass = classof(it);
  return klass === 'DataView' || hasOwn(TypedArrayConstructorsList, klass) || hasOwn(BigIntArrayConstructorsList, klass);
};
var isTypedArray = function isTypedArray(it) {
  if (!isObject(it)) return false;
  var klass = classof(it);
  return hasOwn(TypedArrayConstructorsList, klass) || hasOwn(BigIntArrayConstructorsList, klass);
};
var aTypedArray = function aTypedArray(it) {
  if (isTypedArray(it)) return it;
  throw TypeError('Target is not a typed array');
};
var aTypedArrayConstructor = function aTypedArrayConstructor(C) {
  if (isCallable(C) && (!setPrototypeOf || isPrototypeOf(TypedArray, C))) return C;
  throw TypeError(tryToString(C) + ' is not a typed array constructor');
};
var exportTypedArrayMethod = function exportTypedArrayMethod(KEY, property, forced) {
  if (!DESCRIPTORS) return;
  if (forced) for (var ARRAY in TypedArrayConstructorsList) {
    var TypedArrayConstructor = global[ARRAY];
    if (TypedArrayConstructor && hasOwn(TypedArrayConstructor.prototype, KEY)) try {
      delete TypedArrayConstructor.prototype[KEY];
    } catch (error) {/* empty */}
  }
  if (!TypedArrayPrototype[KEY] || forced) {
    redefine(TypedArrayPrototype, KEY, forced ? property : NATIVE_ARRAY_BUFFER_VIEWS && Int8ArrayPrototype[KEY] || property);
  }
};
var exportTypedArrayStaticMethod = function exportTypedArrayStaticMethod(KEY, property, forced) {
  var ARRAY, TypedArrayConstructor;
  if (!DESCRIPTORS) return;
  if (setPrototypeOf) {
    if (forced) for (ARRAY in TypedArrayConstructorsList) {
      TypedArrayConstructor = global[ARRAY];
      if (TypedArrayConstructor && hasOwn(TypedArrayConstructor, KEY)) try {
        delete TypedArrayConstructor[KEY];
      } catch (error) {/* empty */}
    }
    if (!TypedArray[KEY] || forced) {
      // V8 ~ Chrome 49-50 `%TypedArray%` methods are non-writable non-configurable
      try {
        return redefine(TypedArray, KEY, forced ? property : NATIVE_ARRAY_BUFFER_VIEWS && TypedArray[KEY] || property);
      } catch (error) {/* empty */}
    } else return;
  }
  for (ARRAY in TypedArrayConstructorsList) {
    TypedArrayConstructor = global[ARRAY];
    if (TypedArrayConstructor && (!TypedArrayConstructor[KEY] || forced)) {
      redefine(TypedArrayConstructor, KEY, property);
    }
  }
};
for (NAME in TypedArrayConstructorsList) {
  Constructor = global[NAME];
  Prototype = Constructor && Constructor.prototype;
  if (Prototype) createNonEnumerableProperty(Prototype, TYPED_ARRAY_CONSTRUCTOR, Constructor);else NATIVE_ARRAY_BUFFER_VIEWS = false;
}
for (NAME in BigIntArrayConstructorsList) {
  Constructor = global[NAME];
  Prototype = Constructor && Constructor.prototype;
  if (Prototype) createNonEnumerableProperty(Prototype, TYPED_ARRAY_CONSTRUCTOR, Constructor);
}

// WebKit bug - typed arrays constructors prototype is Object.prototype
if (!NATIVE_ARRAY_BUFFER_VIEWS || !isCallable(TypedArray) || TypedArray === Function.prototype) {
  // eslint-disable-next-line no-shadow -- safe
  TypedArray = function TypedArray() {
    throw TypeError('Incorrect invocation');
  };
  if (NATIVE_ARRAY_BUFFER_VIEWS) for (NAME in TypedArrayConstructorsList) {
    if (global[NAME]) setPrototypeOf(global[NAME], TypedArray);
  }
}
if (!NATIVE_ARRAY_BUFFER_VIEWS || !TypedArrayPrototype || TypedArrayPrototype === ObjectPrototype) {
  TypedArrayPrototype = TypedArray.prototype;
  if (NATIVE_ARRAY_BUFFER_VIEWS) for (NAME in TypedArrayConstructorsList) {
    if (global[NAME]) setPrototypeOf(global[NAME].prototype, TypedArrayPrototype);
  }
}

// WebKit bug - one more object in Uint8ClampedArray prototype chain
if (NATIVE_ARRAY_BUFFER_VIEWS && getPrototypeOf(Uint8ClampedArrayPrototype) !== TypedArrayPrototype) {
  setPrototypeOf(Uint8ClampedArrayPrototype, TypedArrayPrototype);
}
if (DESCRIPTORS && !hasOwn(TypedArrayPrototype, TO_STRING_TAG)) {
  TYPED_ARRAY_TAG_REQIRED = true;
  defineProperty(TypedArrayPrototype, TO_STRING_TAG, {
    get: function get() {
      return isObject(this) ? this[TYPED_ARRAY_TAG] : undefined;
    }
  });
  for (NAME in TypedArrayConstructorsList) if (global[NAME]) {
    createNonEnumerableProperty(global[NAME], TYPED_ARRAY_TAG, NAME);
  }
}
module.exports = {
  NATIVE_ARRAY_BUFFER_VIEWS: NATIVE_ARRAY_BUFFER_VIEWS,
  TYPED_ARRAY_CONSTRUCTOR: TYPED_ARRAY_CONSTRUCTOR,
  TYPED_ARRAY_TAG: TYPED_ARRAY_TAG_REQIRED && TYPED_ARRAY_TAG,
  aTypedArray: aTypedArray,
  aTypedArrayConstructor: aTypedArrayConstructor,
  exportTypedArrayMethod: exportTypedArrayMethod,
  exportTypedArrayStaticMethod: exportTypedArrayStaticMethod,
  isView: isView,
  isTypedArray: isTypedArray,
  TypedArray: TypedArray,
  TypedArrayPrototype: TypedArrayPrototype
};

/***/ }),

/***/ 83124:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(24863);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var DESCRIPTORS = __webpack_require__(15567);
var NATIVE_ARRAY_BUFFER = __webpack_require__(30450);
var FunctionName = __webpack_require__(7081);
var createNonEnumerableProperty = __webpack_require__(48914);
var redefineAll = __webpack_require__(15341);
var fails = __webpack_require__(47044);
var anInstance = __webpack_require__(2868);
var toIntegerOrInfinity = __webpack_require__(26882);
var toLength = __webpack_require__(23417);
var toIndex = __webpack_require__(71265);
var IEEE754 = __webpack_require__(64397);
var getPrototypeOf = __webpack_require__(69548);
var setPrototypeOf = __webpack_require__(3840);
var getOwnPropertyNames = (__webpack_require__(6611).f);
var defineProperty = (__webpack_require__(95892).f);
var arrayFill = __webpack_require__(72864);
var arraySlice = __webpack_require__(73163);
var setToStringTag = __webpack_require__(15216);
var InternalStateModule = __webpack_require__(70172);
var PROPER_FUNCTION_NAME = FunctionName.PROPER;
var CONFIGURABLE_FUNCTION_NAME = FunctionName.CONFIGURABLE;
var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var ARRAY_BUFFER = 'ArrayBuffer';
var DATA_VIEW = 'DataView';
var PROTOTYPE = 'prototype';
var WRONG_LENGTH = 'Wrong length';
var WRONG_INDEX = 'Wrong index';
var NativeArrayBuffer = global[ARRAY_BUFFER];
var $ArrayBuffer = NativeArrayBuffer;
var ArrayBufferPrototype = $ArrayBuffer && $ArrayBuffer[PROTOTYPE];
var $DataView = global[DATA_VIEW];
var DataViewPrototype = $DataView && $DataView[PROTOTYPE];
var ObjectPrototype = Object.prototype;
var Array = global.Array;
var RangeError = global.RangeError;
var fill = uncurryThis(arrayFill);
var reverse = uncurryThis([].reverse);
var packIEEE754 = IEEE754.pack;
var unpackIEEE754 = IEEE754.unpack;
var packInt8 = function packInt8(number) {
  return [number & 0xFF];
};
var packInt16 = function packInt16(number) {
  return [number & 0xFF, number >> 8 & 0xFF];
};
var packInt32 = function packInt32(number) {
  return [number & 0xFF, number >> 8 & 0xFF, number >> 16 & 0xFF, number >> 24 & 0xFF];
};
var unpackInt32 = function unpackInt32(buffer) {
  return buffer[3] << 24 | buffer[2] << 16 | buffer[1] << 8 | buffer[0];
};
var packFloat32 = function packFloat32(number) {
  return packIEEE754(number, 23, 4);
};
var packFloat64 = function packFloat64(number) {
  return packIEEE754(number, 52, 8);
};
var addGetter = function addGetter(Constructor, key) {
  defineProperty(Constructor[PROTOTYPE], key, {
    get: function get() {
      return getInternalState(this)[key];
    }
  });
};
var get = function get(view, count, index, isLittleEndian) {
  var intIndex = toIndex(index);
  var store = getInternalState(view);
  if (intIndex + count > store.byteLength) throw RangeError(WRONG_INDEX);
  var bytes = getInternalState(store.buffer).bytes;
  var start = intIndex + store.byteOffset;
  var pack = arraySlice(bytes, start, start + count);
  return isLittleEndian ? pack : reverse(pack);
};
var set = function set(view, count, index, conversion, value, isLittleEndian) {
  var intIndex = toIndex(index);
  var store = getInternalState(view);
  if (intIndex + count > store.byteLength) throw RangeError(WRONG_INDEX);
  var bytes = getInternalState(store.buffer).bytes;
  var start = intIndex + store.byteOffset;
  var pack = conversion(+value);
  for (var i = 0; i < count; i++) bytes[start + i] = pack[isLittleEndian ? i : count - i - 1];
};
if (!NATIVE_ARRAY_BUFFER) {
  $ArrayBuffer = function ArrayBuffer(length) {
    anInstance(this, ArrayBufferPrototype);
    var byteLength = toIndex(length);
    setInternalState(this, {
      bytes: fill(Array(byteLength), 0),
      byteLength: byteLength
    });
    if (!DESCRIPTORS) this.byteLength = byteLength;
  };
  ArrayBufferPrototype = $ArrayBuffer[PROTOTYPE];
  $DataView = function DataView(buffer, byteOffset, byteLength) {
    anInstance(this, DataViewPrototype);
    anInstance(buffer, ArrayBufferPrototype);
    var bufferLength = getInternalState(buffer).byteLength;
    var offset = toIntegerOrInfinity(byteOffset);
    if (offset < 0 || offset > bufferLength) throw RangeError('Wrong offset');
    byteLength = byteLength === undefined ? bufferLength - offset : toLength(byteLength);
    if (offset + byteLength > bufferLength) throw RangeError(WRONG_LENGTH);
    setInternalState(this, {
      buffer: buffer,
      byteLength: byteLength,
      byteOffset: offset
    });
    if (!DESCRIPTORS) {
      this.buffer = buffer;
      this.byteLength = byteLength;
      this.byteOffset = offset;
    }
  };
  DataViewPrototype = $DataView[PROTOTYPE];
  if (DESCRIPTORS) {
    addGetter($ArrayBuffer, 'byteLength');
    addGetter($DataView, 'buffer');
    addGetter($DataView, 'byteLength');
    addGetter($DataView, 'byteOffset');
  }
  redefineAll(DataViewPrototype, {
    getInt8: function getInt8(byteOffset) {
      return get(this, 1, byteOffset)[0] << 24 >> 24;
    },
    getUint8: function getUint8(byteOffset) {
      return get(this, 1, byteOffset)[0];
    },
    getInt16: function getInt16(byteOffset /* , littleEndian */) {
      var bytes = get(this, 2, byteOffset, arguments.length > 1 ? arguments[1] : undefined);
      return (bytes[1] << 8 | bytes[0]) << 16 >> 16;
    },
    getUint16: function getUint16(byteOffset /* , littleEndian */) {
      var bytes = get(this, 2, byteOffset, arguments.length > 1 ? arguments[1] : undefined);
      return bytes[1] << 8 | bytes[0];
    },
    getInt32: function getInt32(byteOffset /* , littleEndian */) {
      return unpackInt32(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined));
    },
    getUint32: function getUint32(byteOffset /* , littleEndian */) {
      return unpackInt32(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined)) >>> 0;
    },
    getFloat32: function getFloat32(byteOffset /* , littleEndian */) {
      return unpackIEEE754(get(this, 4, byteOffset, arguments.length > 1 ? arguments[1] : undefined), 23);
    },
    getFloat64: function getFloat64(byteOffset /* , littleEndian */) {
      return unpackIEEE754(get(this, 8, byteOffset, arguments.length > 1 ? arguments[1] : undefined), 52);
    },
    setInt8: function setInt8(byteOffset, value) {
      set(this, 1, byteOffset, packInt8, value);
    },
    setUint8: function setUint8(byteOffset, value) {
      set(this, 1, byteOffset, packInt8, value);
    },
    setInt16: function setInt16(byteOffset, value /* , littleEndian */) {
      set(this, 2, byteOffset, packInt16, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setUint16: function setUint16(byteOffset, value /* , littleEndian */) {
      set(this, 2, byteOffset, packInt16, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setInt32: function setInt32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packInt32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setUint32: function setUint32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packInt32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setFloat32: function setFloat32(byteOffset, value /* , littleEndian */) {
      set(this, 4, byteOffset, packFloat32, value, arguments.length > 2 ? arguments[2] : undefined);
    },
    setFloat64: function setFloat64(byteOffset, value /* , littleEndian */) {
      set(this, 8, byteOffset, packFloat64, value, arguments.length > 2 ? arguments[2] : undefined);
    }
  });
} else {
  var INCORRECT_ARRAY_BUFFER_NAME = PROPER_FUNCTION_NAME && NativeArrayBuffer.name !== ARRAY_BUFFER;
  /* eslint-disable no-new -- required for testing */
  if (!fails(function () {
    NativeArrayBuffer(1);
  }) || !fails(function () {
    new NativeArrayBuffer(-1);
  }) || fails(function () {
    new NativeArrayBuffer();
    new NativeArrayBuffer(1.5);
    new NativeArrayBuffer(NaN);
    return INCORRECT_ARRAY_BUFFER_NAME && !CONFIGURABLE_FUNCTION_NAME;
  })) {
    /* eslint-enable no-new -- required for testing */
    $ArrayBuffer = function ArrayBuffer(length) {
      anInstance(this, ArrayBufferPrototype);
      return new NativeArrayBuffer(toIndex(length));
    };
    $ArrayBuffer[PROTOTYPE] = ArrayBufferPrototype;
    for (var keys = getOwnPropertyNames(NativeArrayBuffer), j = 0, key; keys.length > j;) {
      if (!((key = keys[j++]) in $ArrayBuffer)) {
        createNonEnumerableProperty($ArrayBuffer, key, NativeArrayBuffer[key]);
      }
    }
    ArrayBufferPrototype.constructor = $ArrayBuffer;
  } else if (INCORRECT_ARRAY_BUFFER_NAME && CONFIGURABLE_FUNCTION_NAME) {
    createNonEnumerableProperty(NativeArrayBuffer, 'name', ARRAY_BUFFER);
  }

  // WebKit bug - the same parent prototype for typed arrays and data view
  if (setPrototypeOf && getPrototypeOf(DataViewPrototype) !== ObjectPrototype) {
    setPrototypeOf(DataViewPrototype, ObjectPrototype);
  }

  // iOS Safari 7.x bug
  var testView = new $DataView(new $ArrayBuffer(2));
  var $setInt8 = uncurryThis(DataViewPrototype.setInt8);
  testView.setInt8(0, 2147483648);
  testView.setInt8(1, 2147483649);
  if (testView.getInt8(0) || !testView.getInt8(1)) redefineAll(DataViewPrototype, {
    setInt8: function setInt8(byteOffset, value) {
      $setInt8(this, byteOffset, value << 24 >> 24);
    },
    setUint8: function setUint8(byteOffset, value) {
      $setInt8(this, byteOffset, value << 24 >> 24);
    }
  }, {
    unsafe: true
  });
}
setToStringTag($ArrayBuffer, ARRAY_BUFFER);
setToStringTag($DataView, DATA_VIEW);
module.exports = {
  ArrayBuffer: $ArrayBuffer,
  DataView: $DataView
};

/***/ }),

/***/ 8953:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var arrayBufferModule = __webpack_require__(83124);
var setSpecies = __webpack_require__(51334);
var ARRAY_BUFFER = 'ArrayBuffer';
var ArrayBuffer = arrayBufferModule[ARRAY_BUFFER];
var NativeArrayBuffer = global[ARRAY_BUFFER];

// `ArrayBuffer` constructor
// https://tc39.es/ecma262/#sec-arraybuffer-constructor
$({
  global: true,
  forced: NativeArrayBuffer !== ArrayBuffer
}, {
  ArrayBuffer: ArrayBuffer
});
setSpecies(ARRAY_BUFFER);

/***/ }),

/***/ 22956:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;

    // Lookup tables
    var SBOX = [];
    var INV_SBOX = [];
    var SUB_MIX_0 = [];
    var SUB_MIX_1 = [];
    var SUB_MIX_2 = [];
    var SUB_MIX_3 = [];
    var INV_SUB_MIX_0 = [];
    var INV_SUB_MIX_1 = [];
    var INV_SUB_MIX_2 = [];
    var INV_SUB_MIX_3 = [];

    // Compute lookup tables
    (function () {
      // Compute double table
      var d = [];
      for (var i = 0; i < 256; i++) {
        if (i < 128) {
          d[i] = i << 1;
        } else {
          d[i] = i << 1 ^ 0x11b;
        }
      }

      // Walk GF(2^8)
      var x = 0;
      var xi = 0;
      for (var i = 0; i < 256; i++) {
        // Compute sbox
        var sx = xi ^ xi << 1 ^ xi << 2 ^ xi << 3 ^ xi << 4;
        sx = sx >>> 8 ^ sx & 0xff ^ 0x63;
        SBOX[x] = sx;
        INV_SBOX[sx] = x;

        // Compute multiplication
        var x2 = d[x];
        var x4 = d[x2];
        var x8 = d[x4];

        // Compute sub bytes, mix columns tables
        var t = d[sx] * 0x101 ^ sx * 0x1010100;
        SUB_MIX_0[x] = t << 24 | t >>> 8;
        SUB_MIX_1[x] = t << 16 | t >>> 16;
        SUB_MIX_2[x] = t << 8 | t >>> 24;
        SUB_MIX_3[x] = t;

        // Compute inv sub bytes, inv mix columns tables
        var t = x8 * 0x1010101 ^ x4 * 0x10001 ^ x2 * 0x101 ^ x * 0x1010100;
        INV_SUB_MIX_0[sx] = t << 24 | t >>> 8;
        INV_SUB_MIX_1[sx] = t << 16 | t >>> 16;
        INV_SUB_MIX_2[sx] = t << 8 | t >>> 24;
        INV_SUB_MIX_3[sx] = t;

        // Compute next counter
        if (!x) {
          x = xi = 1;
        } else {
          x = x2 ^ d[d[d[x8 ^ x2]]];
          xi ^= d[d[xi]];
        }
      }
    })();

    // Precomputed Rcon lookup
    var RCON = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];

    /**
     * AES block cipher algorithm.
     */
    var AES = C_algo.AES = BlockCipher.extend({
      _doReset: function _doReset() {
        var t;

        // Skip reset of nRounds has been set before and key did not change
        if (this._nRounds && this._keyPriorReset === this._key) {
          return;
        }

        // Shortcuts
        var key = this._keyPriorReset = this._key;
        var keyWords = key.words;
        var keySize = key.sigBytes / 4;

        // Compute number of rounds
        var nRounds = this._nRounds = keySize + 6;

        // Compute number of key schedule rows
        var ksRows = (nRounds + 1) * 4;

        // Compute key schedule
        var keySchedule = this._keySchedule = [];
        for (var ksRow = 0; ksRow < ksRows; ksRow++) {
          if (ksRow < keySize) {
            keySchedule[ksRow] = keyWords[ksRow];
          } else {
            t = keySchedule[ksRow - 1];
            if (!(ksRow % keySize)) {
              // Rot word
              t = t << 8 | t >>> 24;

              // Sub word
              t = SBOX[t >>> 24] << 24 | SBOX[t >>> 16 & 0xff] << 16 | SBOX[t >>> 8 & 0xff] << 8 | SBOX[t & 0xff];

              // Mix Rcon
              t ^= RCON[ksRow / keySize | 0] << 24;
            } else if (keySize > 6 && ksRow % keySize == 4) {
              // Sub word
              t = SBOX[t >>> 24] << 24 | SBOX[t >>> 16 & 0xff] << 16 | SBOX[t >>> 8 & 0xff] << 8 | SBOX[t & 0xff];
            }
            keySchedule[ksRow] = keySchedule[ksRow - keySize] ^ t;
          }
        }

        // Compute inv key schedule
        var invKeySchedule = this._invKeySchedule = [];
        for (var invKsRow = 0; invKsRow < ksRows; invKsRow++) {
          var ksRow = ksRows - invKsRow;
          if (invKsRow % 4) {
            var t = keySchedule[ksRow];
          } else {
            var t = keySchedule[ksRow - 4];
          }
          if (invKsRow < 4 || ksRow <= 4) {
            invKeySchedule[invKsRow] = t;
          } else {
            invKeySchedule[invKsRow] = INV_SUB_MIX_0[SBOX[t >>> 24]] ^ INV_SUB_MIX_1[SBOX[t >>> 16 & 0xff]] ^ INV_SUB_MIX_2[SBOX[t >>> 8 & 0xff]] ^ INV_SUB_MIX_3[SBOX[t & 0xff]];
          }
        }
      },
      encryptBlock: function encryptBlock(M, offset) {
        this._doCryptBlock(M, offset, this._keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX);
      },
      decryptBlock: function decryptBlock(M, offset) {
        // Swap 2nd and 4th rows
        var t = M[offset + 1];
        M[offset + 1] = M[offset + 3];
        M[offset + 3] = t;
        this._doCryptBlock(M, offset, this._invKeySchedule, INV_SUB_MIX_0, INV_SUB_MIX_1, INV_SUB_MIX_2, INV_SUB_MIX_3, INV_SBOX);

        // Inv swap 2nd and 4th rows
        var t = M[offset + 1];
        M[offset + 1] = M[offset + 3];
        M[offset + 3] = t;
      },
      _doCryptBlock: function _doCryptBlock(M, offset, keySchedule, SUB_MIX_0, SUB_MIX_1, SUB_MIX_2, SUB_MIX_3, SBOX) {
        // Shortcut
        var nRounds = this._nRounds;

        // Get input, add round key
        var s0 = M[offset] ^ keySchedule[0];
        var s1 = M[offset + 1] ^ keySchedule[1];
        var s2 = M[offset + 2] ^ keySchedule[2];
        var s3 = M[offset + 3] ^ keySchedule[3];

        // Key schedule row counter
        var ksRow = 4;

        // Rounds
        for (var round = 1; round < nRounds; round++) {
          // Shift rows, sub bytes, mix columns, add round key
          var t0 = SUB_MIX_0[s0 >>> 24] ^ SUB_MIX_1[s1 >>> 16 & 0xff] ^ SUB_MIX_2[s2 >>> 8 & 0xff] ^ SUB_MIX_3[s3 & 0xff] ^ keySchedule[ksRow++];
          var t1 = SUB_MIX_0[s1 >>> 24] ^ SUB_MIX_1[s2 >>> 16 & 0xff] ^ SUB_MIX_2[s3 >>> 8 & 0xff] ^ SUB_MIX_3[s0 & 0xff] ^ keySchedule[ksRow++];
          var t2 = SUB_MIX_0[s2 >>> 24] ^ SUB_MIX_1[s3 >>> 16 & 0xff] ^ SUB_MIX_2[s0 >>> 8 & 0xff] ^ SUB_MIX_3[s1 & 0xff] ^ keySchedule[ksRow++];
          var t3 = SUB_MIX_0[s3 >>> 24] ^ SUB_MIX_1[s0 >>> 16 & 0xff] ^ SUB_MIX_2[s1 >>> 8 & 0xff] ^ SUB_MIX_3[s2 & 0xff] ^ keySchedule[ksRow++];

          // Update state
          s0 = t0;
          s1 = t1;
          s2 = t2;
          s3 = t3;
        }

        // Shift rows, sub bytes, add round key
        var t0 = (SBOX[s0 >>> 24] << 24 | SBOX[s1 >>> 16 & 0xff] << 16 | SBOX[s2 >>> 8 & 0xff] << 8 | SBOX[s3 & 0xff]) ^ keySchedule[ksRow++];
        var t1 = (SBOX[s1 >>> 24] << 24 | SBOX[s2 >>> 16 & 0xff] << 16 | SBOX[s3 >>> 8 & 0xff] << 8 | SBOX[s0 & 0xff]) ^ keySchedule[ksRow++];
        var t2 = (SBOX[s2 >>> 24] << 24 | SBOX[s3 >>> 16 & 0xff] << 16 | SBOX[s0 >>> 8 & 0xff] << 8 | SBOX[s1 & 0xff]) ^ keySchedule[ksRow++];
        var t3 = (SBOX[s3 >>> 24] << 24 | SBOX[s0 >>> 16 & 0xff] << 16 | SBOX[s1 >>> 8 & 0xff] << 8 | SBOX[s2 & 0xff]) ^ keySchedule[ksRow++];

        // Set output
        M[offset] = t0;
        M[offset + 1] = t1;
        M[offset + 2] = t2;
        M[offset + 3] = t3;
      },
      keySize: 256 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.AES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.AES.decrypt(ciphertext, key, cfg);
     */
    C.AES = BlockCipher._createHelper(AES);
  })();
  return CryptoJS.AES;
});

/***/ }),

/***/ 73517:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;
    var N = 16;

    //Origin pbox and sbox, derived from PI
    var ORIG_P = [0x243F6A88, 0x85A308D3, 0x13198A2E, 0x03707344, 0xA4093822, 0x299F31D0, 0x082EFA98, 0xEC4E6C89, 0x452821E6, 0x38D01377, 0xBE5466CF, 0x34E90C6C, 0xC0AC29B7, 0xC97C50DD, 0x3F84D5B5, 0xB5470917, 0x9216D5D9, 0x8979FB1B];
    var ORIG_S = [[0xD1310BA6, 0x98DFB5AC, 0x2FFD72DB, 0xD01ADFB7, 0xB8E1AFED, 0x6A267E96, 0xBA7C9045, 0xF12C7F99, 0x24A19947, 0xB3916CF7, 0x0801F2E2, 0x858EFC16, 0x636920D8, 0x71574E69, 0xA458FEA3, 0xF4933D7E, 0x0D95748F, 0x728EB658, 0x718BCD58, 0x82154AEE, 0x7B54A41D, 0xC25A59B5, 0x9C30D539, 0x2AF26013, 0xC5D1B023, 0x286085F0, 0xCA417918, 0xB8DB38EF, 0x8E79DCB0, 0x603A180E, 0x6C9E0E8B, 0xB01E8A3E, 0xD71577C1, 0xBD314B27, 0x78AF2FDA, 0x55605C60, 0xE65525F3, 0xAA55AB94, 0x57489862, 0x63E81440, 0x55CA396A, 0x2AAB10B6, 0xB4CC5C34, 0x1141E8CE, 0xA15486AF, 0x7C72E993, 0xB3EE1411, 0x636FBC2A, 0x2BA9C55D, 0x741831F6, 0xCE5C3E16, 0x9B87931E, 0xAFD6BA33, 0x6C24CF5C, 0x7A325381, 0x28958677, 0x3B8F4898, 0x6B4BB9AF, 0xC4BFE81B, 0x66282193, 0x61D809CC, 0xFB21A991, 0x487CAC60, 0x5DEC8032, 0xEF845D5D, 0xE98575B1, 0xDC262302, 0xEB651B88, 0x23893E81, 0xD396ACC5, 0x0F6D6FF3, 0x83F44239, 0x2E0B4482, 0xA4842004, 0x69C8F04A, 0x9E1F9B5E, 0x21C66842, 0xF6E96C9A, 0x670C9C61, 0xABD388F0, 0x6A51A0D2, 0xD8542F68, 0x960FA728, 0xAB5133A3, 0x6EEF0B6C, 0x137A3BE4, 0xBA3BF050, 0x7EFB2A98, 0xA1F1651D, 0x39AF0176, 0x66CA593E, 0x82430E88, 0x8CEE8619, 0x456F9FB4, 0x7D84A5C3, 0x3B8B5EBE, 0xE06F75D8, 0x85C12073, 0x401A449F, 0x56C16AA6, 0x4ED3AA62, 0x363F7706, 0x1BFEDF72, 0x429B023D, 0x37D0D724, 0xD00A1248, 0xDB0FEAD3, 0x49F1C09B, 0x075372C9, 0x80991B7B, 0x25D479D8, 0xF6E8DEF7, 0xE3FE501A, 0xB6794C3B, 0x976CE0BD, 0x04C006BA, 0xC1A94FB6, 0x409F60C4, 0x5E5C9EC2, 0x196A2463, 0x68FB6FAF, 0x3E6C53B5, 0x1339B2EB, 0x3B52EC6F, 0x6DFC511F, 0x9B30952C, 0xCC814544, 0xAF5EBD09, 0xBEE3D004, 0xDE334AFD, 0x660F2807, 0x192E4BB3, 0xC0CBA857, 0x45C8740F, 0xD20B5F39, 0xB9D3FBDB, 0x5579C0BD, 0x1A60320A, 0xD6A100C6, 0x402C7279, 0x679F25FE, 0xFB1FA3CC, 0x8EA5E9F8, 0xDB3222F8, 0x3C7516DF, 0xFD616B15, 0x2F501EC8, 0xAD0552AB, 0x323DB5FA, 0xFD238760, 0x53317B48, 0x3E00DF82, 0x9E5C57BB, 0xCA6F8CA0, 0x1A87562E, 0xDF1769DB, 0xD542A8F6, 0x287EFFC3, 0xAC6732C6, 0x8C4F5573, 0x695B27B0, 0xBBCA58C8, 0xE1FFA35D, 0xB8F011A0, 0x10FA3D98, 0xFD2183B8, 0x4AFCB56C, 0x2DD1D35B, 0x9A53E479, 0xB6F84565, 0xD28E49BC, 0x4BFB9790, 0xE1DDF2DA, 0xA4CB7E33, 0x62FB1341, 0xCEE4C6E8, 0xEF20CADA, 0x36774C01, 0xD07E9EFE, 0x2BF11FB4, 0x95DBDA4D, 0xAE909198, 0xEAAD8E71, 0x6B93D5A0, 0xD08ED1D0, 0xAFC725E0, 0x8E3C5B2F, 0x8E7594B7, 0x8FF6E2FB, 0xF2122B64, 0x8888B812, 0x900DF01C, 0x4FAD5EA0, 0x688FC31C, 0xD1CFF191, 0xB3A8C1AD, 0x2F2F2218, 0xBE0E1777, 0xEA752DFE, 0x8B021FA1, 0xE5A0CC0F, 0xB56F74E8, 0x18ACF3D6, 0xCE89E299, 0xB4A84FE0, 0xFD13E0B7, 0x7CC43B81, 0xD2ADA8D9, 0x165FA266, 0x80957705, 0x93CC7314, 0x211A1477, 0xE6AD2065, 0x77B5FA86, 0xC75442F5, 0xFB9D35CF, 0xEBCDAF0C, 0x7B3E89A0, 0xD6411BD3, 0xAE1E7E49, 0x00250E2D, 0x2071B35E, 0x226800BB, 0x57B8E0AF, 0x2464369B, 0xF009B91E, 0x5563911D, 0x59DFA6AA, 0x78C14389, 0xD95A537F, 0x207D5BA2, 0x02E5B9C5, 0x83260376, 0x6295CFA9, 0x11C81968, 0x4E734A41, 0xB3472DCA, 0x7B14A94A, 0x1B510052, 0x9A532915, 0xD60F573F, 0xBC9BC6E4, 0x2B60A476, 0x81E67400, 0x08BA6FB5, 0x571BE91F, 0xF296EC6B, 0x2A0DD915, 0xB6636521, 0xE7B9F9B6, 0xFF34052E, 0xC5855664, 0x53B02D5D, 0xA99F8FA1, 0x08BA4799, 0x6E85076A], [0x4B7A70E9, 0xB5B32944, 0xDB75092E, 0xC4192623, 0xAD6EA6B0, 0x49A7DF7D, 0x9CEE60B8, 0x8FEDB266, 0xECAA8C71, 0x699A17FF, 0x5664526C, 0xC2B19EE1, 0x193602A5, 0x75094C29, 0xA0591340, 0xE4183A3E, 0x3F54989A, 0x5B429D65, 0x6B8FE4D6, 0x99F73FD6, 0xA1D29C07, 0xEFE830F5, 0x4D2D38E6, 0xF0255DC1, 0x4CDD2086, 0x8470EB26, 0x6382E9C6, 0x021ECC5E, 0x09686B3F, 0x3EBAEFC9, 0x3C971814, 0x6B6A70A1, 0x687F3584, 0x52A0E286, 0xB79C5305, 0xAA500737, 0x3E07841C, 0x7FDEAE5C, 0x8E7D44EC, 0x5716F2B8, 0xB03ADA37, 0xF0500C0D, 0xF01C1F04, 0x0200B3FF, 0xAE0CF51A, 0x3CB574B2, 0x25837A58, 0xDC0921BD, 0xD19113F9, 0x7CA92FF6, 0x94324773, 0x22F54701, 0x3AE5E581, 0x37C2DADC, 0xC8B57634, 0x9AF3DDA7, 0xA9446146, 0x0FD0030E, 0xECC8C73E, 0xA4751E41, 0xE238CD99, 0x3BEA0E2F, 0x3280BBA1, 0x183EB331, 0x4E548B38, 0x4F6DB908, 0x6F420D03, 0xF60A04BF, 0x2CB81290, 0x24977C79, 0x5679B072, 0xBCAF89AF, 0xDE9A771F, 0xD9930810, 0xB38BAE12, 0xDCCF3F2E, 0x5512721F, 0x2E6B7124, 0x501ADDE6, 0x9F84CD87, 0x7A584718, 0x7408DA17, 0xBC9F9ABC, 0xE94B7D8C, 0xEC7AEC3A, 0xDB851DFA, 0x63094366, 0xC464C3D2, 0xEF1C1847, 0x3215D908, 0xDD433B37, 0x24C2BA16, 0x12A14D43, 0x2A65C451, 0x50940002, 0x133AE4DD, 0x71DFF89E, 0x10314E55, 0x81AC77D6, 0x5F11199B, 0x043556F1, 0xD7A3C76B, 0x3C11183B, 0x5924A509, 0xF28FE6ED, 0x97F1FBFA, 0x9EBABF2C, 0x1E153C6E, 0x86E34570, 0xEAE96FB1, 0x860E5E0A, 0x5A3E2AB3, 0x771FE71C, 0x4E3D06FA, 0x2965DCB9, 0x99E71D0F, 0x803E89D6, 0x5266C825, 0x2E4CC978, 0x9C10B36A, 0xC6150EBA, 0x94E2EA78, 0xA5FC3C53, 0x1E0A2DF4, 0xF2F74EA7, 0x361D2B3D, 0x1939260F, 0x19C27960, 0x5223A708, 0xF71312B6, 0xEBADFE6E, 0xEAC31F66, 0xE3BC4595, 0xA67BC883, 0xB17F37D1, 0x018CFF28, 0xC332DDEF, 0xBE6C5AA5, 0x65582185, 0x68AB9802, 0xEECEA50F, 0xDB2F953B, 0x2AEF7DAD, 0x5B6E2F84, 0x1521B628, 0x29076170, 0xECDD4775, 0x619F1510, 0x13CCA830, 0xEB61BD96, 0x0334FE1E, 0xAA0363CF, 0xB5735C90, 0x4C70A239, 0xD59E9E0B, 0xCBAADE14, 0xEECC86BC, 0x60622CA7, 0x9CAB5CAB, 0xB2F3846E, 0x648B1EAF, 0x19BDF0CA, 0xA02369B9, 0x655ABB50, 0x40685A32, 0x3C2AB4B3, 0x319EE9D5, 0xC021B8F7, 0x9B540B19, 0x875FA099, 0x95F7997E, 0x623D7DA8, 0xF837889A, 0x97E32D77, 0x11ED935F, 0x16681281, 0x0E358829, 0xC7E61FD6, 0x96DEDFA1, 0x7858BA99, 0x57F584A5, 0x1B227263, 0x9B83C3FF, 0x1AC24696, 0xCDB30AEB, 0x532E3054, 0x8FD948E4, 0x6DBC3128, 0x58EBF2EF, 0x34C6FFEA, 0xFE28ED61, 0xEE7C3C73, 0x5D4A14D9, 0xE864B7E3, 0x42105D14, 0x203E13E0, 0x45EEE2B6, 0xA3AAABEA, 0xDB6C4F15, 0xFACB4FD0, 0xC742F442, 0xEF6ABBB5, 0x654F3B1D, 0x41CD2105, 0xD81E799E, 0x86854DC7, 0xE44B476A, 0x3D816250, 0xCF62A1F2, 0x5B8D2646, 0xFC8883A0, 0xC1C7B6A3, 0x7F1524C3, 0x69CB7492, 0x47848A0B, 0x5692B285, 0x095BBF00, 0xAD19489D, 0x1462B174, 0x23820E00, 0x58428D2A, 0x0C55F5EA, 0x1DADF43E, 0x233F7061, 0x3372F092, 0x8D937E41, 0xD65FECF1, 0x6C223BDB, 0x7CDE3759, 0xCBEE7460, 0x4085F2A7, 0xCE77326E, 0xA6078084, 0x19F8509E, 0xE8EFD855, 0x61D99735, 0xA969A7AA, 0xC50C06C2, 0x5A04ABFC, 0x800BCADC, 0x9E447A2E, 0xC3453484, 0xFDD56705, 0x0E1E9EC9, 0xDB73DBD3, 0x105588CD, 0x675FDA79, 0xE3674340, 0xC5C43465, 0x713E38D8, 0x3D28F89E, 0xF16DFF20, 0x153E21E7, 0x8FB03D4A, 0xE6E39F2B, 0xDB83ADF7], [0xE93D5A68, 0x948140F7, 0xF64C261C, 0x94692934, 0x411520F7, 0x7602D4F7, 0xBCF46B2E, 0xD4A20068, 0xD4082471, 0x3320F46A, 0x43B7D4B7, 0x500061AF, 0x1E39F62E, 0x97244546, 0x14214F74, 0xBF8B8840, 0x4D95FC1D, 0x96B591AF, 0x70F4DDD3, 0x66A02F45, 0xBFBC09EC, 0x03BD9785, 0x7FAC6DD0, 0x31CB8504, 0x96EB27B3, 0x55FD3941, 0xDA2547E6, 0xABCA0A9A, 0x28507825, 0x530429F4, 0x0A2C86DA, 0xE9B66DFB, 0x68DC1462, 0xD7486900, 0x680EC0A4, 0x27A18DEE, 0x4F3FFEA2, 0xE887AD8C, 0xB58CE006, 0x7AF4D6B6, 0xAACE1E7C, 0xD3375FEC, 0xCE78A399, 0x406B2A42, 0x20FE9E35, 0xD9F385B9, 0xEE39D7AB, 0x3B124E8B, 0x1DC9FAF7, 0x4B6D1856, 0x26A36631, 0xEAE397B2, 0x3A6EFA74, 0xDD5B4332, 0x6841E7F7, 0xCA7820FB, 0xFB0AF54E, 0xD8FEB397, 0x454056AC, 0xBA489527, 0x55533A3A, 0x20838D87, 0xFE6BA9B7, 0xD096954B, 0x55A867BC, 0xA1159A58, 0xCCA92963, 0x99E1DB33, 0xA62A4A56, 0x3F3125F9, 0x5EF47E1C, 0x9029317C, 0xFDF8E802, 0x04272F70, 0x80BB155C, 0x05282CE3, 0x95C11548, 0xE4C66D22, 0x48C1133F, 0xC70F86DC, 0x07F9C9EE, 0x41041F0F, 0x404779A4, 0x5D886E17, 0x325F51EB, 0xD59BC0D1, 0xF2BCC18F, 0x41113564, 0x257B7834, 0x602A9C60, 0xDFF8E8A3, 0x1F636C1B, 0x0E12B4C2, 0x02E1329E, 0xAF664FD1, 0xCAD18115, 0x6B2395E0, 0x333E92E1, 0x3B240B62, 0xEEBEB922, 0x85B2A20E, 0xE6BA0D99, 0xDE720C8C, 0x2DA2F728, 0xD0127845, 0x95B794FD, 0x647D0862, 0xE7CCF5F0, 0x5449A36F, 0x877D48FA, 0xC39DFD27, 0xF33E8D1E, 0x0A476341, 0x992EFF74, 0x3A6F6EAB, 0xF4F8FD37, 0xA812DC60, 0xA1EBDDF8, 0x991BE14C, 0xDB6E6B0D, 0xC67B5510, 0x6D672C37, 0x2765D43B, 0xDCD0E804, 0xF1290DC7, 0xCC00FFA3, 0xB5390F92, 0x690FED0B, 0x667B9FFB, 0xCEDB7D9C, 0xA091CF0B, 0xD9155EA3, 0xBB132F88, 0x515BAD24, 0x7B9479BF, 0x763BD6EB, 0x37392EB3, 0xCC115979, 0x8026E297, 0xF42E312D, 0x6842ADA7, 0xC66A2B3B, 0x12754CCC, 0x782EF11C, 0x6A124237, 0xB79251E7, 0x06A1BBE6, 0x4BFB6350, 0x1A6B1018, 0x11CAEDFA, 0x3D25BDD8, 0xE2E1C3C9, 0x44421659, 0x0A121386, 0xD90CEC6E, 0xD5ABEA2A, 0x64AF674E, 0xDA86A85F, 0xBEBFE988, 0x64E4C3FE, 0x9DBC8057, 0xF0F7C086, 0x60787BF8, 0x6003604D, 0xD1FD8346, 0xF6381FB0, 0x7745AE04, 0xD736FCCC, 0x83426B33, 0xF01EAB71, 0xB0804187, 0x3C005E5F, 0x77A057BE, 0xBDE8AE24, 0x55464299, 0xBF582E61, 0x4E58F48F, 0xF2DDFDA2, 0xF474EF38, 0x8789BDC2, 0x5366F9C3, 0xC8B38E74, 0xB475F255, 0x46FCD9B9, 0x7AEB2661, 0x8B1DDF84, 0x846A0E79, 0x915F95E2, 0x466E598E, 0x20B45770, 0x8CD55591, 0xC902DE4C, 0xB90BACE1, 0xBB8205D0, 0x11A86248, 0x7574A99E, 0xB77F19B6, 0xE0A9DC09, 0x662D09A1, 0xC4324633, 0xE85A1F02, 0x09F0BE8C, 0x4A99A025, 0x1D6EFE10, 0x1AB93D1D, 0x0BA5A4DF, 0xA186F20F, 0x2868F169, 0xDCB7DA83, 0x573906FE, 0xA1E2CE9B, 0x4FCD7F52, 0x50115E01, 0xA70683FA, 0xA002B5C4, 0x0DE6D027, 0x9AF88C27, 0x773F8641, 0xC3604C06, 0x61A806B5, 0xF0177A28, 0xC0F586E0, 0x006058AA, 0x30DC7D62, 0x11E69ED7, 0x2338EA63, 0x53C2DD94, 0xC2C21634, 0xBBCBEE56, 0x90BCB6DE, 0xEBFC7DA1, 0xCE591D76, 0x6F05E409, 0x4B7C0188, 0x39720A3D, 0x7C927C24, 0x86E3725F, 0x724D9DB9, 0x1AC15BB4, 0xD39EB8FC, 0xED545578, 0x08FCA5B5, 0xD83D7CD3, 0x4DAD0FC4, 0x1E50EF5E, 0xB161E6F8, 0xA28514D9, 0x6C51133C, 0x6FD5C7E7, 0x56E14EC4, 0x362ABFCE, 0xDDC6C837, 0xD79A3234, 0x92638212, 0x670EFA8E, 0x406000E0], [0x3A39CE37, 0xD3FAF5CF, 0xABC27737, 0x5AC52D1B, 0x5CB0679E, 0x4FA33742, 0xD3822740, 0x99BC9BBE, 0xD5118E9D, 0xBF0F7315, 0xD62D1C7E, 0xC700C47B, 0xB78C1B6B, 0x21A19045, 0xB26EB1BE, 0x6A366EB4, 0x5748AB2F, 0xBC946E79, 0xC6A376D2, 0x6549C2C8, 0x530FF8EE, 0x468DDE7D, 0xD5730A1D, 0x4CD04DC6, 0x2939BBDB, 0xA9BA4650, 0xAC9526E8, 0xBE5EE304, 0xA1FAD5F0, 0x6A2D519A, 0x63EF8CE2, 0x9A86EE22, 0xC089C2B8, 0x43242EF6, 0xA51E03AA, 0x9CF2D0A4, 0x83C061BA, 0x9BE96A4D, 0x8FE51550, 0xBA645BD6, 0x2826A2F9, 0xA73A3AE1, 0x4BA99586, 0xEF5562E9, 0xC72FEFD3, 0xF752F7DA, 0x3F046F69, 0x77FA0A59, 0x80E4A915, 0x87B08601, 0x9B09E6AD, 0x3B3EE593, 0xE990FD5A, 0x9E34D797, 0x2CF0B7D9, 0x022B8B51, 0x96D5AC3A, 0x017DA67D, 0xD1CF3ED6, 0x7C7D2D28, 0x1F9F25CF, 0xADF2B89B, 0x5AD6B472, 0x5A88F54C, 0xE029AC71, 0xE019A5E6, 0x47B0ACFD, 0xED93FA9B, 0xE8D3C48D, 0x283B57CC, 0xF8D56629, 0x79132E28, 0x785F0191, 0xED756055, 0xF7960E44, 0xE3D35E8C, 0x15056DD4, 0x88F46DBA, 0x03A16125, 0x0564F0BD, 0xC3EB9E15, 0x3C9057A2, 0x97271AEC, 0xA93A072A, 0x1B3F6D9B, 0x1E6321F5, 0xF59C66FB, 0x26DCF319, 0x7533D928, 0xB155FDF5, 0x03563482, 0x8ABA3CBB, 0x28517711, 0xC20AD9F8, 0xABCC5167, 0xCCAD925F, 0x4DE81751, 0x3830DC8E, 0x379D5862, 0x9320F991, 0xEA7A90C2, 0xFB3E7BCE, 0x5121CE64, 0x774FBE32, 0xA8B6E37E, 0xC3293D46, 0x48DE5369, 0x6413E680, 0xA2AE0810, 0xDD6DB224, 0x69852DFD, 0x09072166, 0xB39A460A, 0x6445C0DD, 0x586CDECF, 0x1C20C8AE, 0x5BBEF7DD, 0x1B588D40, 0xCCD2017F, 0x6BB4E3BB, 0xDDA26A7E, 0x3A59FF45, 0x3E350A44, 0xBCB4CDD5, 0x72EACEA8, 0xFA6484BB, 0x8D6612AE, 0xBF3C6F47, 0xD29BE463, 0x542F5D9E, 0xAEC2771B, 0xF64E6370, 0x740E0D8D, 0xE75B1357, 0xF8721671, 0xAF537D5D, 0x4040CB08, 0x4EB4E2CC, 0x34D2466A, 0x0115AF84, 0xE1B00428, 0x95983A1D, 0x06B89FB4, 0xCE6EA048, 0x6F3F3B82, 0x3520AB82, 0x011A1D4B, 0x277227F8, 0x611560B1, 0xE7933FDC, 0xBB3A792B, 0x344525BD, 0xA08839E1, 0x51CE794B, 0x2F32C9B7, 0xA01FBAC9, 0xE01CC87E, 0xBCC7D1F6, 0xCF0111C3, 0xA1E8AAC7, 0x1A908749, 0xD44FBD9A, 0xD0DADECB, 0xD50ADA38, 0x0339C32A, 0xC6913667, 0x8DF9317C, 0xE0B12B4F, 0xF79E59B7, 0x43F5BB3A, 0xF2D519FF, 0x27D9459C, 0xBF97222C, 0x15E6FC2A, 0x0F91FC71, 0x9B941525, 0xFAE59361, 0xCEB69CEB, 0xC2A86459, 0x12BAA8D1, 0xB6C1075E, 0xE3056A0C, 0x10D25065, 0xCB03A442, 0xE0EC6E0E, 0x1698DB3B, 0x4C98A0BE, 0x3278E964, 0x9F1F9532, 0xE0D392DF, 0xD3A0342B, 0x8971F21E, 0x1B0A7441, 0x4BA3348C, 0xC5BE7120, 0xC37632D8, 0xDF359F8D, 0x9B992F2E, 0xE60B6F47, 0x0FE3F11D, 0xE54CDA54, 0x1EDAD891, 0xCE6279CF, 0xCD3E7E6F, 0x1618B166, 0xFD2C1D05, 0x848FD2C5, 0xF6FB2299, 0xF523F357, 0xA6327623, 0x93A83531, 0x56CCCD02, 0xACF08162, 0x5A75EBB5, 0x6E163697, 0x88D273CC, 0xDE966292, 0x81B949D0, 0x4C50901B, 0x71C65614, 0xE6C6C7BD, 0x327A140A, 0x45E1D006, 0xC3F27B9A, 0xC9AA53FD, 0x62A80F00, 0xBB25BFE2, 0x35BDD2F6, 0x71126905, 0xB2040222, 0xB6CBCF7C, 0xCD769C2B, 0x53113EC0, 0x1640E3D3, 0x38ABBD60, 0x2547ADF0, 0xBA38209C, 0xF746CE76, 0x77AFA1C5, 0x20756060, 0x85CBFE4E, 0x8AE88DD8, 0x7AAAF9B0, 0x4CF9AA7E, 0x1948C25C, 0x02FB8A8C, 0x01C36AE4, 0xD6EBE1F9, 0x90D4F869, 0xA65CDEA0, 0x3F09252D, 0xC208E69F, 0xB74E6132, 0xCE77E25B, 0x578FDFE3, 0x3AC372E6]];
    var BLOWFISH_CTX = {
      pbox: [],
      sbox: []
    };
    function F(ctx, x) {
      var a = x >> 24 & 0xFF;
      var b = x >> 16 & 0xFF;
      var c = x >> 8 & 0xFF;
      var d = x & 0xFF;
      var y = ctx.sbox[0][a] + ctx.sbox[1][b];
      y = y ^ ctx.sbox[2][c];
      y = y + ctx.sbox[3][d];
      return y;
    }
    function BlowFish_Encrypt(ctx, left, right) {
      var Xl = left;
      var Xr = right;
      var temp;
      for (var i = 0; i < N; ++i) {
        Xl = Xl ^ ctx.pbox[i];
        Xr = F(ctx, Xl) ^ Xr;
        temp = Xl;
        Xl = Xr;
        Xr = temp;
      }
      temp = Xl;
      Xl = Xr;
      Xr = temp;
      Xr = Xr ^ ctx.pbox[N];
      Xl = Xl ^ ctx.pbox[N + 1];
      return {
        left: Xl,
        right: Xr
      };
    }
    function BlowFish_Decrypt(ctx, left, right) {
      var Xl = left;
      var Xr = right;
      var temp;
      for (var i = N + 1; i > 1; --i) {
        Xl = Xl ^ ctx.pbox[i];
        Xr = F(ctx, Xl) ^ Xr;
        temp = Xl;
        Xl = Xr;
        Xr = temp;
      }
      temp = Xl;
      Xl = Xr;
      Xr = temp;
      Xr = Xr ^ ctx.pbox[1];
      Xl = Xl ^ ctx.pbox[0];
      return {
        left: Xl,
        right: Xr
      };
    }

    /**
     * Initialization ctx's pbox and sbox.
     *
     * @param {Object} ctx The object has pbox and sbox.
     * @param {Array} key An array of 32-bit words.
     * @param {int} keysize The length of the key.
     *
     * @example
     *
     *     BlowFishInit(BLOWFISH_CTX, key, 128/32);
     */
    function BlowFishInit(ctx, key, keysize) {
      for (var Row = 0; Row < 4; Row++) {
        ctx.sbox[Row] = [];
        for (var Col = 0; Col < 256; Col++) {
          ctx.sbox[Row][Col] = ORIG_S[Row][Col];
        }
      }
      var keyIndex = 0;
      for (var index = 0; index < N + 2; index++) {
        ctx.pbox[index] = ORIG_P[index] ^ key[keyIndex];
        keyIndex++;
        if (keyIndex >= keysize) {
          keyIndex = 0;
        }
      }
      var Data1 = 0;
      var Data2 = 0;
      var res = 0;
      for (var i = 0; i < N + 2; i += 2) {
        res = BlowFish_Encrypt(ctx, Data1, Data2);
        Data1 = res.left;
        Data2 = res.right;
        ctx.pbox[i] = Data1;
        ctx.pbox[i + 1] = Data2;
      }
      for (var _i = 0; _i < 4; _i++) {
        for (var j = 0; j < 256; j += 2) {
          res = BlowFish_Encrypt(ctx, Data1, Data2);
          Data1 = res.left;
          Data2 = res.right;
          ctx.sbox[_i][j] = Data1;
          ctx.sbox[_i][j + 1] = Data2;
        }
      }
      return true;
    }

    /**
     * Blowfish block cipher algorithm.
     */
    var Blowfish = C_algo.Blowfish = BlockCipher.extend({
      _doReset: function _doReset() {
        // Skip reset of nRounds has been set before and key did not change
        if (this._keyPriorReset === this._key) {
          return;
        }

        // Shortcuts
        var key = this._keyPriorReset = this._key;
        var keyWords = key.words;
        var keySize = key.sigBytes / 4;

        //Initialization pbox and sbox
        BlowFishInit(BLOWFISH_CTX, keyWords, keySize);
      },
      encryptBlock: function encryptBlock(M, offset) {
        var res = BlowFish_Encrypt(BLOWFISH_CTX, M[offset], M[offset + 1]);
        M[offset] = res.left;
        M[offset + 1] = res.right;
      },
      decryptBlock: function decryptBlock(M, offset) {
        var res = BlowFish_Decrypt(BLOWFISH_CTX, M[offset], M[offset + 1]);
        M[offset] = res.left;
        M[offset + 1] = res.right;
      },
      blockSize: 64 / 32,
      keySize: 128 / 32,
      ivSize: 64 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.Blowfish.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.Blowfish.decrypt(ciphertext, key, cfg);
     */
    C.Blowfish = BlockCipher._createHelper(Blowfish);
  })();
  return CryptoJS.Blowfish;
});

/***/ }),

/***/ 51270:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
__webpack_require__(20731);
__webpack_require__(23913);
__webpack_require__(14032);
__webpack_require__(57114);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(82747));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Cipher core components.
   */
  CryptoJS.lib.Cipher || function (undefined) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm;
    var C_enc = C.enc;
    var Utf8 = C_enc.Utf8;
    var Base64 = C_enc.Base64;
    var C_algo = C.algo;
    var EvpKDF = C_algo.EvpKDF;

    /**
     * Abstract base cipher template.
     *
     * @property {number} keySize This cipher's key size. Default: 4 (128 bits)
     * @property {number} ivSize This cipher's IV size. Default: 4 (128 bits)
     * @property {number} _ENC_XFORM_MODE A constant representing encryption mode.
     * @property {number} _DEC_XFORM_MODE A constant representing decryption mode.
     */
    var Cipher = C_lib.Cipher = BufferedBlockAlgorithm.extend({
      /**
       * Configuration options.
       *
       * @property {WordArray} iv The IV to use for this operation.
       */
      cfg: Base.extend(),
      /**
       * Creates this cipher in encryption mode.
       *
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {Cipher} A cipher instance.
       *
       * @static
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.createEncryptor(keyWordArray, { iv: ivWordArray });
       */
      createEncryptor: function createEncryptor(key, cfg) {
        return this.create(this._ENC_XFORM_MODE, key, cfg);
      },
      /**
       * Creates this cipher in decryption mode.
       *
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {Cipher} A cipher instance.
       *
       * @static
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.createDecryptor(keyWordArray, { iv: ivWordArray });
       */
      createDecryptor: function createDecryptor(key, cfg) {
        return this.create(this._DEC_XFORM_MODE, key, cfg);
      },
      /**
       * Initializes a newly created cipher.
       *
       * @param {number} xformMode Either the encryption or decryption transormation mode constant.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @example
       *
       *     var cipher = CryptoJS.algo.AES.create(CryptoJS.algo.AES._ENC_XFORM_MODE, keyWordArray, { iv: ivWordArray });
       */
      init: function init(xformMode, key, cfg) {
        // Apply config defaults
        this.cfg = this.cfg.extend(cfg);

        // Store transform mode and key
        this._xformMode = xformMode;
        this._key = key;

        // Set initial values
        this.reset();
      },
      /**
       * Resets this cipher to its initial state.
       *
       * @example
       *
       *     cipher.reset();
       */
      reset: function reset() {
        // Reset data buffer
        BufferedBlockAlgorithm.reset.call(this);

        // Perform concrete-cipher logic
        this._doReset();
      },
      /**
       * Adds data to be encrypted or decrypted.
       *
       * @param {WordArray|string} dataUpdate The data to encrypt or decrypt.
       *
       * @return {WordArray} The data after processing.
       *
       * @example
       *
       *     var encrypted = cipher.process('data');
       *     var encrypted = cipher.process(wordArray);
       */
      process: function process(dataUpdate) {
        // Append
        this._append(dataUpdate);

        // Process available blocks
        return this._process();
      },
      /**
       * Finalizes the encryption or decryption process.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} dataUpdate The final data to encrypt or decrypt.
       *
       * @return {WordArray} The data after final processing.
       *
       * @example
       *
       *     var encrypted = cipher.finalize();
       *     var encrypted = cipher.finalize('data');
       *     var encrypted = cipher.finalize(wordArray);
       */
      finalize: function finalize(dataUpdate) {
        // Final data update
        if (dataUpdate) {
          this._append(dataUpdate);
        }

        // Perform concrete-cipher logic
        var finalProcessedData = this._doFinalize();
        return finalProcessedData;
      },
      keySize: 128 / 32,
      ivSize: 128 / 32,
      _ENC_XFORM_MODE: 1,
      _DEC_XFORM_MODE: 2,
      /**
       * Creates shortcut functions to a cipher's object interface.
       *
       * @param {Cipher} cipher The cipher to create a helper for.
       *
       * @return {Object} An object with encrypt and decrypt shortcut functions.
       *
       * @static
       *
       * @example
       *
       *     var AES = CryptoJS.lib.Cipher._createHelper(CryptoJS.algo.AES);
       */
      _createHelper: function () {
        function selectCipherStrategy(key) {
          if (typeof key == 'string') {
            return PasswordBasedCipher;
          } else {
            return SerializableCipher;
          }
        }
        return function (cipher) {
          return {
            encrypt: function encrypt(message, key, cfg) {
              return selectCipherStrategy(key).encrypt(cipher, message, key, cfg);
            },
            decrypt: function decrypt(ciphertext, key, cfg) {
              return selectCipherStrategy(key).decrypt(cipher, ciphertext, key, cfg);
            }
          };
        };
      }()
    });

    /**
     * Abstract base stream cipher template.
     *
     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 1 (32 bits)
     */
    var StreamCipher = C_lib.StreamCipher = Cipher.extend({
      _doFinalize: function _doFinalize() {
        // Process partial blocks
        var finalProcessedBlocks = this._process(!!'flush');
        return finalProcessedBlocks;
      },
      blockSize: 1
    });

    /**
     * Mode namespace.
     */
    var C_mode = C.mode = {};

    /**
     * Abstract base block cipher mode template.
     */
    var BlockCipherMode = C_lib.BlockCipherMode = Base.extend({
      /**
       * Creates this mode for encryption.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @static
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.createEncryptor(cipher, iv.words);
       */
      createEncryptor: function createEncryptor(cipher, iv) {
        return this.Encryptor.create(cipher, iv);
      },
      /**
       * Creates this mode for decryption.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @static
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.createDecryptor(cipher, iv.words);
       */
      createDecryptor: function createDecryptor(cipher, iv) {
        return this.Decryptor.create(cipher, iv);
      },
      /**
       * Initializes a newly created mode.
       *
       * @param {Cipher} cipher A block cipher instance.
       * @param {Array} iv The IV words.
       *
       * @example
       *
       *     var mode = CryptoJS.mode.CBC.Encryptor.create(cipher, iv.words);
       */
      init: function init(cipher, iv) {
        this._cipher = cipher;
        this._iv = iv;
      }
    });

    /**
     * Cipher Block Chaining mode.
     */
    var CBC = C_mode.CBC = function () {
      /**
       * Abstract base CBC mode.
       */
      var CBC = BlockCipherMode.extend();

      /**
       * CBC encryptor.
       */
      CBC.Encryptor = CBC.extend({
        /**
         * Processes the data block at offset.
         *
         * @param {Array} words The data words to operate on.
         * @param {number} offset The offset where the block starts.
         *
         * @example
         *
         *     mode.processBlock(data.words, offset);
         */
        processBlock: function processBlock(words, offset) {
          // Shortcuts
          var cipher = this._cipher;
          var blockSize = cipher.blockSize;

          // XOR and encrypt
          xorBlock.call(this, words, offset, blockSize);
          cipher.encryptBlock(words, offset);

          // Remember this block to use with next block
          this._prevBlock = words.slice(offset, offset + blockSize);
        }
      });

      /**
       * CBC decryptor.
       */
      CBC.Decryptor = CBC.extend({
        /**
         * Processes the data block at offset.
         *
         * @param {Array} words The data words to operate on.
         * @param {number} offset The offset where the block starts.
         *
         * @example
         *
         *     mode.processBlock(data.words, offset);
         */
        processBlock: function processBlock(words, offset) {
          // Shortcuts
          var cipher = this._cipher;
          var blockSize = cipher.blockSize;

          // Remember this block to use with next block
          var thisBlock = words.slice(offset, offset + blockSize);

          // Decrypt and XOR
          cipher.decryptBlock(words, offset);
          xorBlock.call(this, words, offset, blockSize);

          // This block becomes the previous block
          this._prevBlock = thisBlock;
        }
      });
      function xorBlock(words, offset, blockSize) {
        var block;

        // Shortcut
        var iv = this._iv;

        // Choose mixing block
        if (iv) {
          block = iv;

          // Remove IV for subsequent blocks
          this._iv = undefined;
        } else {
          block = this._prevBlock;
        }

        // XOR blocks
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= block[i];
        }
      }
      return CBC;
    }();

    /**
     * Padding namespace.
     */
    var C_pad = C.pad = {};

    /**
     * PKCS #5/7 padding strategy.
     */
    var Pkcs7 = C_pad.Pkcs7 = {
      /**
       * Pads data using the algorithm defined in PKCS #5/7.
       *
       * @param {WordArray} data The data to pad.
       * @param {number} blockSize The multiple that the data should be padded to.
       *
       * @static
       *
       * @example
       *
       *     CryptoJS.pad.Pkcs7.pad(wordArray, 4);
       */
      pad: function pad(data, blockSize) {
        // Shortcut
        var blockSizeBytes = blockSize * 4;

        // Count padding bytes
        var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

        // Create padding word
        var paddingWord = nPaddingBytes << 24 | nPaddingBytes << 16 | nPaddingBytes << 8 | nPaddingBytes;

        // Create padding
        var paddingWords = [];
        for (var i = 0; i < nPaddingBytes; i += 4) {
          paddingWords.push(paddingWord);
        }
        var padding = WordArray.create(paddingWords, nPaddingBytes);

        // Add padding
        data.concat(padding);
      },
      /**
       * Unpads data that had been padded using the algorithm defined in PKCS #5/7.
       *
       * @param {WordArray} data The data to unpad.
       *
       * @static
       *
       * @example
       *
       *     CryptoJS.pad.Pkcs7.unpad(wordArray);
       */
      unpad: function unpad(data) {
        // Get number of padding bytes from last byte
        var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

        // Remove padding
        data.sigBytes -= nPaddingBytes;
      }
    };

    /**
     * Abstract base block cipher template.
     *
     * @property {number} blockSize The number of 32-bit words this cipher operates on. Default: 4 (128 bits)
     */
    var BlockCipher = C_lib.BlockCipher = Cipher.extend({
      /**
       * Configuration options.
       *
       * @property {Mode} mode The block mode to use. Default: CBC
       * @property {Padding} padding The padding strategy to use. Default: Pkcs7
       */
      cfg: Cipher.cfg.extend({
        mode: CBC,
        padding: Pkcs7
      }),
      reset: function reset() {
        var modeCreator;

        // Reset cipher
        Cipher.reset.call(this);

        // Shortcuts
        var cfg = this.cfg;
        var iv = cfg.iv;
        var mode = cfg.mode;

        // Reset block mode
        if (this._xformMode == this._ENC_XFORM_MODE) {
          modeCreator = mode.createEncryptor;
        } else /* if (this._xformMode == this._DEC_XFORM_MODE) */{
            modeCreator = mode.createDecryptor;
            // Keep at least one block in the buffer for unpadding
            this._minBufferSize = 1;
          }
        if (this._mode && this._mode.__creator == modeCreator) {
          this._mode.init(this, iv && iv.words);
        } else {
          this._mode = modeCreator.call(mode, this, iv && iv.words);
          this._mode.__creator = modeCreator;
        }
      },
      _doProcessBlock: function _doProcessBlock(words, offset) {
        this._mode.processBlock(words, offset);
      },
      _doFinalize: function _doFinalize() {
        var finalProcessedBlocks;

        // Shortcut
        var padding = this.cfg.padding;

        // Finalize
        if (this._xformMode == this._ENC_XFORM_MODE) {
          // Pad data
          padding.pad(this._data, this.blockSize);

          // Process final blocks
          finalProcessedBlocks = this._process(!!'flush');
        } else /* if (this._xformMode == this._DEC_XFORM_MODE) */{
            // Process final blocks
            finalProcessedBlocks = this._process(!!'flush');

            // Unpad data
            padding.unpad(finalProcessedBlocks);
          }
        return finalProcessedBlocks;
      },
      blockSize: 128 / 32
    });

    /**
     * A collection of cipher parameters.
     *
     * @property {WordArray} ciphertext The raw ciphertext.
     * @property {WordArray} key The key to this ciphertext.
     * @property {WordArray} iv The IV used in the ciphering operation.
     * @property {WordArray} salt The salt used with a key derivation function.
     * @property {Cipher} algorithm The cipher algorithm.
     * @property {Mode} mode The block mode used in the ciphering operation.
     * @property {Padding} padding The padding scheme used in the ciphering operation.
     * @property {number} blockSize The block size of the cipher.
     * @property {Format} formatter The default formatting strategy to convert this cipher params object to a string.
     */
    var CipherParams = C_lib.CipherParams = Base.extend({
      /**
       * Initializes a newly created cipher params object.
       *
       * @param {Object} cipherParams An object with any of the possible cipher parameters.
       *
       * @example
       *
       *     var cipherParams = CryptoJS.lib.CipherParams.create({
       *         ciphertext: ciphertextWordArray,
       *         key: keyWordArray,
       *         iv: ivWordArray,
       *         salt: saltWordArray,
       *         algorithm: CryptoJS.algo.AES,
       *         mode: CryptoJS.mode.CBC,
       *         padding: CryptoJS.pad.PKCS7,
       *         blockSize: 4,
       *         formatter: CryptoJS.format.OpenSSL
       *     });
       */
      init: function init(cipherParams) {
        this.mixIn(cipherParams);
      },
      /**
       * Converts this cipher params object to a string.
       *
       * @param {Format} formatter (Optional) The formatting strategy to use.
       *
       * @return {string} The stringified cipher params.
       *
       * @throws Error If neither the formatter nor the default formatter is set.
       *
       * @example
       *
       *     var string = cipherParams + '';
       *     var string = cipherParams.toString();
       *     var string = cipherParams.toString(CryptoJS.format.OpenSSL);
       */
      toString: function toString(formatter) {
        return (formatter || this.formatter).stringify(this);
      }
    });

    /**
     * Format namespace.
     */
    var C_format = C.format = {};

    /**
     * OpenSSL formatting strategy.
     */
    var OpenSSLFormatter = C_format.OpenSSL = {
      /**
       * Converts a cipher params object to an OpenSSL-compatible string.
       *
       * @param {CipherParams} cipherParams The cipher params object.
       *
       * @return {string} The OpenSSL-compatible string.
       *
       * @static
       *
       * @example
       *
       *     var openSSLString = CryptoJS.format.OpenSSL.stringify(cipherParams);
       */
      stringify: function stringify(cipherParams) {
        var wordArray;

        // Shortcuts
        var ciphertext = cipherParams.ciphertext;
        var salt = cipherParams.salt;

        // Format
        if (salt) {
          wordArray = WordArray.create([0x53616c74, 0x65645f5f]).concat(salt).concat(ciphertext);
        } else {
          wordArray = ciphertext;
        }
        return wordArray.toString(Base64);
      },
      /**
       * Converts an OpenSSL-compatible string to a cipher params object.
       *
       * @param {string} openSSLStr The OpenSSL-compatible string.
       *
       * @return {CipherParams} The cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var cipherParams = CryptoJS.format.OpenSSL.parse(openSSLString);
       */
      parse: function parse(openSSLStr) {
        var salt;

        // Parse base64
        var ciphertext = Base64.parse(openSSLStr);

        // Shortcut
        var ciphertextWords = ciphertext.words;

        // Test for salt
        if (ciphertextWords[0] == 0x53616c74 && ciphertextWords[1] == 0x65645f5f) {
          // Extract salt
          salt = WordArray.create(ciphertextWords.slice(2, 4));

          // Remove salt from ciphertext
          ciphertextWords.splice(0, 4);
          ciphertext.sigBytes -= 16;
        }
        return CipherParams.create({
          ciphertext: ciphertext,
          salt: salt
        });
      }
    };

    /**
     * A cipher wrapper that returns ciphertext as a serializable cipher params object.
     */
    var SerializableCipher = C_lib.SerializableCipher = Base.extend({
      /**
       * Configuration options.
       *
       * @property {Formatter} format The formatting strategy to convert cipher param objects to and from a string. Default: OpenSSL
       */
      cfg: Base.extend({
        format: OpenSSLFormatter
      }),
      /**
       * Encrypts a message.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {WordArray|string} message The message to encrypt.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {CipherParams} A cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key);
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv });
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher.encrypt(CryptoJS.algo.AES, message, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       */
      encrypt: function encrypt(cipher, message, key, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Encrypt
        var encryptor = cipher.createEncryptor(key, cfg);
        var ciphertext = encryptor.finalize(message);

        // Shortcut
        var cipherCfg = encryptor.cfg;

        // Create and return serializable cipher params
        return CipherParams.create({
          ciphertext: ciphertext,
          key: key,
          iv: cipherCfg.iv,
          algorithm: cipher,
          mode: cipherCfg.mode,
          padding: cipherCfg.padding,
          blockSize: cipher.blockSize,
          formatter: cfg.format
        });
      },
      /**
       * Decrypts serialized ciphertext.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
       * @param {WordArray} key The key.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {WordArray} The plaintext.
       *
       * @static
       *
       * @example
       *
       *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       *     var plaintext = CryptoJS.lib.SerializableCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, key, { iv: iv, format: CryptoJS.format.OpenSSL });
       */
      decrypt: function decrypt(cipher, ciphertext, key, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Convert string to CipherParams
        ciphertext = this._parse(ciphertext, cfg.format);

        // Decrypt
        var plaintext = cipher.createDecryptor(key, cfg).finalize(ciphertext.ciphertext);
        return plaintext;
      },
      /**
       * Converts serialized ciphertext to CipherParams,
       * else assumed CipherParams already and returns ciphertext unchanged.
       *
       * @param {CipherParams|string} ciphertext The ciphertext.
       * @param {Formatter} format The formatting strategy to use to parse serialized ciphertext.
       *
       * @return {CipherParams} The unserialized ciphertext.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.SerializableCipher._parse(ciphertextStringOrParams, format);
       */
      _parse: function _parse(ciphertext, format) {
        if (typeof ciphertext == 'string') {
          return format.parse(ciphertext, this);
        } else {
          return ciphertext;
        }
      }
    });

    /**
     * Key derivation function namespace.
     */
    var C_kdf = C.kdf = {};

    /**
     * OpenSSL key derivation function.
     */
    var OpenSSLKdf = C_kdf.OpenSSL = {
      /**
       * Derives a key and IV from a password.
       *
       * @param {string} password The password to derive from.
       * @param {number} keySize The size in words of the key to generate.
       * @param {number} ivSize The size in words of the IV to generate.
       * @param {WordArray|string} salt (Optional) A 64-bit salt to use. If omitted, a salt will be generated randomly.
       *
       * @return {CipherParams} A cipher params object with the key, IV, and salt.
       *
       * @static
       *
       * @example
       *
       *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32);
       *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32, 'saltsalt');
       */
      execute: function execute(password, keySize, ivSize, salt, hasher) {
        // Generate random salt
        if (!salt) {
          salt = WordArray.random(64 / 8);
        }

        // Derive key and IV
        if (!hasher) {
          var key = EvpKDF.create({
            keySize: keySize + ivSize
          }).compute(password, salt);
        } else {
          var key = EvpKDF.create({
            keySize: keySize + ivSize,
            hasher: hasher
          }).compute(password, salt);
        }

        // Separate key and IV
        var iv = WordArray.create(key.words.slice(keySize), ivSize * 4);
        key.sigBytes = keySize * 4;

        // Return params
        return CipherParams.create({
          key: key,
          iv: iv,
          salt: salt
        });
      }
    };

    /**
     * A serializable cipher wrapper that derives the key from a password,
     * and returns ciphertext as a serializable cipher params object.
     */
    var PasswordBasedCipher = C_lib.PasswordBasedCipher = SerializableCipher.extend({
      /**
       * Configuration options.
       *
       * @property {KDF} kdf The key derivation function to use to generate a key and IV from a password. Default: OpenSSL
       */
      cfg: SerializableCipher.cfg.extend({
        kdf: OpenSSLKdf
      }),
      /**
       * Encrypts a message using a password.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {WordArray|string} message The message to encrypt.
       * @param {string} password The password.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {CipherParams} A cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password');
       *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher.encrypt(CryptoJS.algo.AES, message, 'password', { format: CryptoJS.format.OpenSSL });
       */
      encrypt: function encrypt(cipher, message, password, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Derive key and other params
        var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, cfg.salt, cfg.hasher);

        // Add IV to config
        cfg.iv = derivedParams.iv;

        // Encrypt
        var ciphertext = SerializableCipher.encrypt.call(this, cipher, message, derivedParams.key, cfg);

        // Mix in derived params
        ciphertext.mixIn(derivedParams);
        return ciphertext;
      },
      /**
       * Decrypts serialized ciphertext using a password.
       *
       * @param {Cipher} cipher The cipher algorithm to use.
       * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
       * @param {string} password The password.
       * @param {Object} cfg (Optional) The configuration options to use for this operation.
       *
       * @return {WordArray} The plaintext.
       *
       * @static
       *
       * @example
       *
       *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, formattedCiphertext, 'password', { format: CryptoJS.format.OpenSSL });
       *     var plaintext = CryptoJS.lib.PasswordBasedCipher.decrypt(CryptoJS.algo.AES, ciphertextParams, 'password', { format: CryptoJS.format.OpenSSL });
       */
      decrypt: function decrypt(cipher, ciphertext, password, cfg) {
        // Apply config defaults
        cfg = this.cfg.extend(cfg);

        // Convert string to CipherParams
        ciphertext = this._parse(ciphertext, cfg.format);

        // Derive key and other params
        var derivedParams = cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, ciphertext.salt, cfg.hasher);

        // Add IV to config
        cfg.iv = derivedParams.iv;

        // Decrypt
        var plaintext = SerializableCipher.decrypt.call(this, cipher, ciphertext, derivedParams.key, cfg);
        return plaintext;
      }
    });
  }();
});

/***/ }),

/***/ 48352:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
__webpack_require__(81755);
__webpack_require__(94845);
__webpack_require__(20731);
__webpack_require__(23913);
__webpack_require__(8953);
__webpack_require__(14032);
__webpack_require__(57114);
__webpack_require__(59735);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
__webpack_require__(49109);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory();
  } else {}
})(void 0, function () {
  /*globals window, global, require*/

  /**
   * CryptoJS core components.
   */
  var CryptoJS = CryptoJS || function (Math, undefined) {
    var crypto;

    // Native crypto from window (Browser)
    if (typeof window !== 'undefined' && window.crypto) {
      crypto = window.crypto;
    }

    // Native crypto in web worker (Browser)
    if (typeof self !== 'undefined' && self.crypto) {
      crypto = self.crypto;
    }

    // Native crypto from worker
    if (typeof globalThis !== 'undefined' && globalThis.crypto) {
      crypto = globalThis.crypto;
    }

    // Native (experimental IE 11) crypto from window (Browser)
    if (!crypto && typeof window !== 'undefined' && window.msCrypto) {
      crypto = window.msCrypto;
    }

    // Native crypto from global (NodeJS)
    if (!crypto && typeof __webpack_require__.g !== 'undefined' && __webpack_require__.g.crypto) {
      crypto = __webpack_require__.g.crypto;
    }

    // Native crypto import via require (NodeJS)
    if (!crypto && "function" === 'function') {
      try {
        crypto = __webpack_require__(50477);
      } catch (err) {}
    }

    /*
     * Cryptographically secure pseudorandom number generator
     *
     * As Math.random() is cryptographically not safe to use
     */
    var cryptoSecureRandomInt = function cryptoSecureRandomInt() {
      if (crypto) {
        // Use getRandomValues method (Browser)
        if (typeof crypto.getRandomValues === 'function') {
          try {
            return crypto.getRandomValues(new Uint32Array(1))[0];
          } catch (err) {}
        }

        // Use randomBytes method (NodeJS)
        if (typeof crypto.randomBytes === 'function') {
          try {
            return crypto.randomBytes(4).readInt32LE();
          } catch (err) {}
        }
      }
      throw new Error('Native crypto module could not be used to get secure random number.');
    };

    /*
     * Local polyfill of Object.create
      */
    var create = Object.create || function () {
      function F() {}
      return function (obj) {
        var subtype;
        F.prototype = obj;
        subtype = new F();
        F.prototype = null;
        return subtype;
      };
    }();

    /**
     * CryptoJS namespace.
     */
    var C = {};

    /**
     * Library namespace.
     */
    var C_lib = C.lib = {};

    /**
     * Base object for prototypal inheritance.
     */
    var Base = C_lib.Base = function () {
      return {
        /**
         * Creates a new object that inherits from this object.
         *
         * @param {Object} overrides Properties to copy into the new object.
         *
         * @return {Object} The new object.
         *
         * @static
         *
         * @example
         *
         *     var MyType = CryptoJS.lib.Base.extend({
         *         field: 'value',
         *
         *         method: function () {
         *         }
         *     });
         */
        extend: function extend(overrides) {
          // Spawn
          var subtype = create(this);

          // Augment
          if (overrides) {
            subtype.mixIn(overrides);
          }

          // Create default initializer
          if (!subtype.hasOwnProperty('init') || this.init === subtype.init) {
            subtype.init = function () {
              subtype.$super.init.apply(this, arguments);
            };
          }

          // Initializer's prototype is the subtype object
          subtype.init.prototype = subtype;

          // Reference supertype
          subtype.$super = this;
          return subtype;
        },
        /**
         * Extends this object and runs the init method.
         * Arguments to create() will be passed to init().
         *
         * @return {Object} The new object.
         *
         * @static
         *
         * @example
         *
         *     var instance = MyType.create();
         */
        create: function create() {
          var instance = this.extend();
          instance.init.apply(instance, arguments);
          return instance;
        },
        /**
         * Initializes a newly created object.
         * Override this method to add some logic when your objects are created.
         *
         * @example
         *
         *     var MyType = CryptoJS.lib.Base.extend({
         *         init: function () {
         *             // ...
         *         }
         *     });
         */
        init: function init() {},
        /**
         * Copies properties into this object.
         *
         * @param {Object} properties The properties to mix in.
         *
         * @example
         *
         *     MyType.mixIn({
         *         field: 'value'
         *     });
         */
        mixIn: function mixIn(properties) {
          for (var propertyName in properties) {
            if (properties.hasOwnProperty(propertyName)) {
              this[propertyName] = properties[propertyName];
            }
          }

          // IE won't copy toString using the loop above
          if (properties.hasOwnProperty('toString')) {
            this.toString = properties.toString;
          }
        },
        /**
         * Creates a copy of this object.
         *
         * @return {Object} The clone.
         *
         * @example
         *
         *     var clone = instance.clone();
         */
        clone: function clone() {
          return this.init.prototype.extend(this);
        }
      };
    }();

    /**
     * An array of 32-bit words.
     *
     * @property {Array} words The array of 32-bit words.
     * @property {number} sigBytes The number of significant bytes in this word array.
     */
    var WordArray = C_lib.WordArray = Base.extend({
      /**
       * Initializes a newly created word array.
       *
       * @param {Array} words (Optional) An array of 32-bit words.
       * @param {number} sigBytes (Optional) The number of significant bytes in the words.
       *
       * @example
       *
       *     var wordArray = CryptoJS.lib.WordArray.create();
       *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607]);
       *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607], 6);
       */
      init: function init(words, sigBytes) {
        words = this.words = words || [];
        if (sigBytes != undefined) {
          this.sigBytes = sigBytes;
        } else {
          this.sigBytes = words.length * 4;
        }
      },
      /**
       * Converts this word array to a string.
       *
       * @param {Encoder} encoder (Optional) The encoding strategy to use. Default: CryptoJS.enc.Hex
       *
       * @return {string} The stringified word array.
       *
       * @example
       *
       *     var string = wordArray + '';
       *     var string = wordArray.toString();
       *     var string = wordArray.toString(CryptoJS.enc.Utf8);
       */
      toString: function toString(encoder) {
        return (encoder || Hex).stringify(this);
      },
      /**
       * Concatenates a word array to this word array.
       *
       * @param {WordArray} wordArray The word array to append.
       *
       * @return {WordArray} This word array.
       *
       * @example
       *
       *     wordArray1.concat(wordArray2);
       */
      concat: function concat(wordArray) {
        // Shortcuts
        var thisWords = this.words;
        var thatWords = wordArray.words;
        var thisSigBytes = this.sigBytes;
        var thatSigBytes = wordArray.sigBytes;

        // Clamp excess bits
        this.clamp();

        // Concat
        if (thisSigBytes % 4) {
          // Copy one byte at a time
          for (var i = 0; i < thatSigBytes; i++) {
            var thatByte = thatWords[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
            thisWords[thisSigBytes + i >>> 2] |= thatByte << 24 - (thisSigBytes + i) % 4 * 8;
          }
        } else {
          // Copy one word at a time
          for (var j = 0; j < thatSigBytes; j += 4) {
            thisWords[thisSigBytes + j >>> 2] = thatWords[j >>> 2];
          }
        }
        this.sigBytes += thatSigBytes;

        // Chainable
        return this;
      },
      /**
       * Removes insignificant bits.
       *
       * @example
       *
       *     wordArray.clamp();
       */
      clamp: function clamp() {
        // Shortcuts
        var words = this.words;
        var sigBytes = this.sigBytes;

        // Clamp
        words[sigBytes >>> 2] &= 0xffffffff << 32 - sigBytes % 4 * 8;
        words.length = Math.ceil(sigBytes / 4);
      },
      /**
       * Creates a copy of this word array.
       *
       * @return {WordArray} The clone.
       *
       * @example
       *
       *     var clone = wordArray.clone();
       */
      clone: function clone() {
        var clone = Base.clone.call(this);
        clone.words = this.words.slice(0);
        return clone;
      },
      /**
       * Creates a word array filled with random bytes.
       *
       * @param {number} nBytes The number of random bytes to generate.
       *
       * @return {WordArray} The random word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.lib.WordArray.random(16);
       */
      random: function random(nBytes) {
        var words = [];
        for (var i = 0; i < nBytes; i += 4) {
          words.push(cryptoSecureRandomInt());
        }
        return new WordArray.init(words, nBytes);
      }
    });

    /**
     * Encoder namespace.
     */
    var C_enc = C.enc = {};

    /**
     * Hex encoding strategy.
     */
    var Hex = C_enc.Hex = {
      /**
       * Converts a word array to a hex string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The hex string.
       *
       * @static
       *
       * @example
       *
       *     var hexString = CryptoJS.enc.Hex.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var hexChars = [];
        for (var i = 0; i < sigBytes; i++) {
          var bite = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          hexChars.push((bite >>> 4).toString(16));
          hexChars.push((bite & 0x0f).toString(16));
        }
        return hexChars.join('');
      },
      /**
       * Converts a hex string to a word array.
       *
       * @param {string} hexStr The hex string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Hex.parse(hexString);
       */
      parse: function parse(hexStr) {
        // Shortcut
        var hexStrLength = hexStr.length;

        // Convert
        var words = [];
        for (var i = 0; i < hexStrLength; i += 2) {
          words[i >>> 3] |= parseInt(hexStr.substr(i, 2), 16) << 24 - i % 8 * 4;
        }
        return new WordArray.init(words, hexStrLength / 2);
      }
    };

    /**
     * Latin1 encoding strategy.
     */
    var Latin1 = C_enc.Latin1 = {
      /**
       * Converts a word array to a Latin1 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The Latin1 string.
       *
       * @static
       *
       * @example
       *
       *     var latin1String = CryptoJS.enc.Latin1.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var latin1Chars = [];
        for (var i = 0; i < sigBytes; i++) {
          var bite = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          latin1Chars.push(String.fromCharCode(bite));
        }
        return latin1Chars.join('');
      },
      /**
       * Converts a Latin1 string to a word array.
       *
       * @param {string} latin1Str The Latin1 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Latin1.parse(latin1String);
       */
      parse: function parse(latin1Str) {
        // Shortcut
        var latin1StrLength = latin1Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < latin1StrLength; i++) {
          words[i >>> 2] |= (latin1Str.charCodeAt(i) & 0xff) << 24 - i % 4 * 8;
        }
        return new WordArray.init(words, latin1StrLength);
      }
    };

    /**
     * UTF-8 encoding strategy.
     */
    var Utf8 = C_enc.Utf8 = {
      /**
       * Converts a word array to a UTF-8 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-8 string.
       *
       * @static
       *
       * @example
       *
       *     var utf8String = CryptoJS.enc.Utf8.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        try {
          return decodeURIComponent(escape(Latin1.stringify(wordArray)));
        } catch (e) {
          throw new Error('Malformed UTF-8 data');
        }
      },
      /**
       * Converts a UTF-8 string to a word array.
       *
       * @param {string} utf8Str The UTF-8 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf8.parse(utf8String);
       */
      parse: function parse(utf8Str) {
        return Latin1.parse(unescape(encodeURIComponent(utf8Str)));
      }
    };

    /**
     * Abstract buffered block algorithm template.
     *
     * The property blockSize must be implemented in a concrete subtype.
     *
     * @property {number} _minBufferSize The number of blocks that should be kept unprocessed in the buffer. Default: 0
     */
    var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm = Base.extend({
      /**
       * Resets this block algorithm's data buffer to its initial state.
       *
       * @example
       *
       *     bufferedBlockAlgorithm.reset();
       */
      reset: function reset() {
        // Initial values
        this._data = new WordArray.init();
        this._nDataBytes = 0;
      },
      /**
       * Adds new data to this block algorithm's buffer.
       *
       * @param {WordArray|string} data The data to append. Strings are converted to a WordArray using UTF-8.
       *
       * @example
       *
       *     bufferedBlockAlgorithm._append('data');
       *     bufferedBlockAlgorithm._append(wordArray);
       */
      _append: function _append(data) {
        // Convert string to WordArray, else assume WordArray already
        if (typeof data == 'string') {
          data = Utf8.parse(data);
        }

        // Append
        this._data.concat(data);
        this._nDataBytes += data.sigBytes;
      },
      /**
       * Processes available data blocks.
       *
       * This method invokes _doProcessBlock(offset), which must be implemented by a concrete subtype.
       *
       * @param {boolean} doFlush Whether all blocks and partial blocks should be processed.
       *
       * @return {WordArray} The processed data.
       *
       * @example
       *
       *     var processedData = bufferedBlockAlgorithm._process();
       *     var processedData = bufferedBlockAlgorithm._process(!!'flush');
       */
      _process: function _process(doFlush) {
        var processedWords;

        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var dataSigBytes = data.sigBytes;
        var blockSize = this.blockSize;
        var blockSizeBytes = blockSize * 4;

        // Count blocks ready
        var nBlocksReady = dataSigBytes / blockSizeBytes;
        if (doFlush) {
          // Round up to include partial blocks
          nBlocksReady = Math.ceil(nBlocksReady);
        } else {
          // Round down to include only full blocks,
          // less the number of blocks that must remain in the buffer
          nBlocksReady = Math.max((nBlocksReady | 0) - this._minBufferSize, 0);
        }

        // Count words ready
        var nWordsReady = nBlocksReady * blockSize;

        // Count bytes ready
        var nBytesReady = Math.min(nWordsReady * 4, dataSigBytes);

        // Process blocks
        if (nWordsReady) {
          for (var offset = 0; offset < nWordsReady; offset += blockSize) {
            // Perform concrete-algorithm logic
            this._doProcessBlock(dataWords, offset);
          }

          // Remove processed words
          processedWords = dataWords.splice(0, nWordsReady);
          data.sigBytes -= nBytesReady;
        }

        // Return processed words
        return new WordArray.init(processedWords, nBytesReady);
      },
      /**
       * Creates a copy of this object.
       *
       * @return {Object} The clone.
       *
       * @example
       *
       *     var clone = bufferedBlockAlgorithm.clone();
       */
      clone: function clone() {
        var clone = Base.clone.call(this);
        clone._data = this._data.clone();
        return clone;
      },
      _minBufferSize: 0
    });

    /**
     * Abstract hasher template.
     *
     * @property {number} blockSize The number of 32-bit words this hasher operates on. Default: 16 (512 bits)
     */
    var Hasher = C_lib.Hasher = BufferedBlockAlgorithm.extend({
      /**
       * Configuration options.
       */
      cfg: Base.extend(),
      /**
       * Initializes a newly created hasher.
       *
       * @param {Object} cfg (Optional) The configuration options to use for this hash computation.
       *
       * @example
       *
       *     var hasher = CryptoJS.algo.SHA256.create();
       */
      init: function init(cfg) {
        // Apply config defaults
        this.cfg = this.cfg.extend(cfg);

        // Set initial values
        this.reset();
      },
      /**
       * Resets this hasher to its initial state.
       *
       * @example
       *
       *     hasher.reset();
       */
      reset: function reset() {
        // Reset data buffer
        BufferedBlockAlgorithm.reset.call(this);

        // Perform concrete-hasher logic
        this._doReset();
      },
      /**
       * Updates this hasher with a message.
       *
       * @param {WordArray|string} messageUpdate The message to append.
       *
       * @return {Hasher} This hasher.
       *
       * @example
       *
       *     hasher.update('message');
       *     hasher.update(wordArray);
       */
      update: function update(messageUpdate) {
        // Append
        this._append(messageUpdate);

        // Update the hash
        this._process();

        // Chainable
        return this;
      },
      /**
       * Finalizes the hash computation.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} messageUpdate (Optional) A final message update.
       *
       * @return {WordArray} The hash.
       *
       * @example
       *
       *     var hash = hasher.finalize();
       *     var hash = hasher.finalize('message');
       *     var hash = hasher.finalize(wordArray);
       */
      finalize: function finalize(messageUpdate) {
        // Final message update
        if (messageUpdate) {
          this._append(messageUpdate);
        }

        // Perform concrete-hasher logic
        var hash = this._doFinalize();
        return hash;
      },
      blockSize: 512 / 32,
      /**
       * Creates a shortcut function to a hasher's object interface.
       *
       * @param {Hasher} hasher The hasher to create a helper for.
       *
       * @return {Function} The shortcut function.
       *
       * @static
       *
       * @example
       *
       *     var SHA256 = CryptoJS.lib.Hasher._createHelper(CryptoJS.algo.SHA256);
       */
      _createHelper: function _createHelper(hasher) {
        return function (message, cfg) {
          return new hasher.init(cfg).finalize(message);
        };
      },
      /**
       * Creates a shortcut function to the HMAC's object interface.
       *
       * @param {Hasher} hasher The hasher to use in this HMAC helper.
       *
       * @return {Function} The shortcut function.
       *
       * @static
       *
       * @example
       *
       *     var HmacSHA256 = CryptoJS.lib.Hasher._createHmacHelper(CryptoJS.algo.SHA256);
       */
      _createHmacHelper: function _createHmacHelper(hasher) {
        return function (message, key) {
          return new C_algo.HMAC.init(hasher, key).finalize(message);
        };
      }
    });

    /**
     * Algorithm namespace.
     */
    var C_algo = C.algo = {};
    return C;
  }(Math);
  return CryptoJS;
});

/***/ }),

/***/ 66947:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(94845);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * Base64 encoding strategy.
     */
    var Base64 = C_enc.Base64 = {
      /**
       * Converts a word array to a Base64 string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The Base64 string.
       *
       * @static
       *
       * @example
       *
       *     var base64String = CryptoJS.enc.Base64.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;
        var map = this._map;

        // Clamp excess bits
        wordArray.clamp();

        // Convert
        var base64Chars = [];
        for (var i = 0; i < sigBytes; i += 3) {
          var byte1 = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          var byte2 = words[i + 1 >>> 2] >>> 24 - (i + 1) % 4 * 8 & 0xff;
          var byte3 = words[i + 2 >>> 2] >>> 24 - (i + 2) % 4 * 8 & 0xff;
          var triplet = byte1 << 16 | byte2 << 8 | byte3;
          for (var j = 0; j < 4 && i + j * 0.75 < sigBytes; j++) {
            base64Chars.push(map.charAt(triplet >>> 6 * (3 - j) & 0x3f));
          }
        }

        // Add padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          while (base64Chars.length % 4) {
            base64Chars.push(paddingChar);
          }
        }
        return base64Chars.join('');
      },
      /**
       * Converts a Base64 string to a word array.
       *
       * @param {string} base64Str The Base64 string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Base64.parse(base64String);
       */
      parse: function parse(base64Str) {
        // Shortcuts
        var base64StrLength = base64Str.length;
        var map = this._map;
        var reverseMap = this._reverseMap;
        if (!reverseMap) {
          reverseMap = this._reverseMap = [];
          for (var j = 0; j < map.length; j++) {
            reverseMap[map.charCodeAt(j)] = j;
          }
        }

        // Ignore padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          var paddingIndex = base64Str.indexOf(paddingChar);
          if (paddingIndex !== -1) {
            base64StrLength = paddingIndex;
          }
        }

        // Convert
        return parseLoop(base64Str, base64StrLength, reverseMap);
      },
      _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
    };
    function parseLoop(base64Str, base64StrLength, reverseMap) {
      var words = [];
      var nBytes = 0;
      for (var i = 0; i < base64StrLength; i++) {
        if (i % 4) {
          var bits1 = reverseMap[base64Str.charCodeAt(i - 1)] << i % 4 * 2;
          var bits2 = reverseMap[base64Str.charCodeAt(i)] >>> 6 - i % 4 * 2;
          var bitsCombined = bits1 | bits2;
          words[nBytes >>> 2] |= bitsCombined << 24 - nBytes % 4 * 8;
          nBytes++;
        }
      }
      return WordArray.create(words, nBytes);
    }
  })();
  return CryptoJS.enc.Base64;
});

/***/ }),

/***/ 16190:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(94845);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * Base64url encoding strategy.
     */
    var Base64url = C_enc.Base64url = {
      /**
       * Converts a word array to a Base64url string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @param {boolean} urlSafe Whether to use url safe
       *
       * @return {string} The Base64url string.
       *
       * @static
       *
       * @example
       *
       *     var base64String = CryptoJS.enc.Base64url.stringify(wordArray);
       */
      stringify: function stringify(wordArray, urlSafe) {
        if (urlSafe === undefined) {
          urlSafe = true;
        }
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;
        var map = urlSafe ? this._safe_map : this._map;

        // Clamp excess bits
        wordArray.clamp();

        // Convert
        var base64Chars = [];
        for (var i = 0; i < sigBytes; i += 3) {
          var byte1 = words[i >>> 2] >>> 24 - i % 4 * 8 & 0xff;
          var byte2 = words[i + 1 >>> 2] >>> 24 - (i + 1) % 4 * 8 & 0xff;
          var byte3 = words[i + 2 >>> 2] >>> 24 - (i + 2) % 4 * 8 & 0xff;
          var triplet = byte1 << 16 | byte2 << 8 | byte3;
          for (var j = 0; j < 4 && i + j * 0.75 < sigBytes; j++) {
            base64Chars.push(map.charAt(triplet >>> 6 * (3 - j) & 0x3f));
          }
        }

        // Add padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          while (base64Chars.length % 4) {
            base64Chars.push(paddingChar);
          }
        }
        return base64Chars.join('');
      },
      /**
       * Converts a Base64url string to a word array.
       *
       * @param {string} base64Str The Base64url string.
       *
       * @param {boolean} urlSafe Whether to use url safe
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Base64url.parse(base64String);
       */
      parse: function parse(base64Str, urlSafe) {
        if (urlSafe === undefined) {
          urlSafe = true;
        }

        // Shortcuts
        var base64StrLength = base64Str.length;
        var map = urlSafe ? this._safe_map : this._map;
        var reverseMap = this._reverseMap;
        if (!reverseMap) {
          reverseMap = this._reverseMap = [];
          for (var j = 0; j < map.length; j++) {
            reverseMap[map.charCodeAt(j)] = j;
          }
        }

        // Ignore padding
        var paddingChar = map.charAt(64);
        if (paddingChar) {
          var paddingIndex = base64Str.indexOf(paddingChar);
          if (paddingIndex !== -1) {
            base64StrLength = paddingIndex;
          }
        }

        // Convert
        return parseLoop(base64Str, base64StrLength, reverseMap);
      },
      _map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
      _safe_map: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'
    };
    function parseLoop(base64Str, base64StrLength, reverseMap) {
      var words = [];
      var nBytes = 0;
      for (var i = 0; i < base64StrLength; i++) {
        if (i % 4) {
          var bits1 = reverseMap[base64Str.charCodeAt(i - 1)] << i % 4 * 2;
          var bits2 = reverseMap[base64Str.charCodeAt(i)] >>> 6 - i % 4 * 2;
          var bitsCombined = bits1 | bits2;
          words[nBytes >>> 2] |= bitsCombined << 24 - nBytes % 4 * 8;
          nBytes++;
        }
      }
      return WordArray.create(words, nBytes);
    }
  })();
  return CryptoJS.enc.Base64url;
});

/***/ }),

/***/ 2256:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(94845);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_enc = C.enc;

    /**
     * UTF-16 BE encoding strategy.
     */
    var Utf16BE = C_enc.Utf16 = C_enc.Utf16BE = {
      /**
       * Converts a word array to a UTF-16 BE string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-16 BE string.
       *
       * @static
       *
       * @example
       *
       *     var utf16String = CryptoJS.enc.Utf16.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var utf16Chars = [];
        for (var i = 0; i < sigBytes; i += 2) {
          var codePoint = words[i >>> 2] >>> 16 - i % 4 * 8 & 0xffff;
          utf16Chars.push(String.fromCharCode(codePoint));
        }
        return utf16Chars.join('');
      },
      /**
       * Converts a UTF-16 BE string to a word array.
       *
       * @param {string} utf16Str The UTF-16 BE string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf16.parse(utf16String);
       */
      parse: function parse(utf16Str) {
        // Shortcut
        var utf16StrLength = utf16Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < utf16StrLength; i++) {
          words[i >>> 1] |= utf16Str.charCodeAt(i) << 16 - i % 2 * 16;
        }
        return WordArray.create(words, utf16StrLength * 2);
      }
    };

    /**
     * UTF-16 LE encoding strategy.
     */
    C_enc.Utf16LE = {
      /**
       * Converts a word array to a UTF-16 LE string.
       *
       * @param {WordArray} wordArray The word array.
       *
       * @return {string} The UTF-16 LE string.
       *
       * @static
       *
       * @example
       *
       *     var utf16Str = CryptoJS.enc.Utf16LE.stringify(wordArray);
       */
      stringify: function stringify(wordArray) {
        // Shortcuts
        var words = wordArray.words;
        var sigBytes = wordArray.sigBytes;

        // Convert
        var utf16Chars = [];
        for (var i = 0; i < sigBytes; i += 2) {
          var codePoint = swapEndian(words[i >>> 2] >>> 16 - i % 4 * 8 & 0xffff);
          utf16Chars.push(String.fromCharCode(codePoint));
        }
        return utf16Chars.join('');
      },
      /**
       * Converts a UTF-16 LE string to a word array.
       *
       * @param {string} utf16Str The UTF-16 LE string.
       *
       * @return {WordArray} The word array.
       *
       * @static
       *
       * @example
       *
       *     var wordArray = CryptoJS.enc.Utf16LE.parse(utf16Str);
       */
      parse: function parse(utf16Str) {
        // Shortcut
        var utf16StrLength = utf16Str.length;

        // Convert
        var words = [];
        for (var i = 0; i < utf16StrLength; i++) {
          words[i >>> 1] |= swapEndian(utf16Str.charCodeAt(i) << 16 - i % 2 * 16);
        }
        return WordArray.create(words, utf16StrLength * 2);
      }
    };
    function swapEndian(word) {
      return word << 8 & 0xff00ff00 | word >>> 8 & 0x00ff00ff;
    }
  })();
  return CryptoJS.enc.Utf16;
});

/***/ }),

/***/ 82747:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(37146), __webpack_require__(4420));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var MD5 = C_algo.MD5;

    /**
     * This key derivation function is meant to conform with EVP_BytesToKey.
     * www.openssl.org/docs/crypto/EVP_BytesToKey.html
     */
    var EvpKDF = C_algo.EvpKDF = Base.extend({
      /**
       * Configuration options.
       *
       * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
       * @property {Hasher} hasher The hash algorithm to use. Default: MD5
       * @property {number} iterations The number of iterations to perform. Default: 1
       */
      cfg: Base.extend({
        keySize: 128 / 32,
        hasher: MD5,
        iterations: 1
      }),
      /**
       * Initializes a newly created key derivation function.
       *
       * @param {Object} cfg (Optional) The configuration options to use for the derivation.
       *
       * @example
       *
       *     var kdf = CryptoJS.algo.EvpKDF.create();
       *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8 });
       *     var kdf = CryptoJS.algo.EvpKDF.create({ keySize: 8, iterations: 1000 });
       */
      init: function init(cfg) {
        this.cfg = this.cfg.extend(cfg);
      },
      /**
       * Derives a key from a password.
       *
       * @param {WordArray|string} password The password.
       * @param {WordArray|string} salt A salt.
       *
       * @return {WordArray} The derived key.
       *
       * @example
       *
       *     var key = kdf.compute(password, salt);
       */
      compute: function compute(password, salt) {
        var block;

        // Shortcut
        var cfg = this.cfg;

        // Init hasher
        var hasher = cfg.hasher.create();

        // Initial values
        var derivedKey = WordArray.create();

        // Shortcuts
        var derivedKeyWords = derivedKey.words;
        var keySize = cfg.keySize;
        var iterations = cfg.iterations;

        // Generate key
        while (derivedKeyWords.length < keySize) {
          if (block) {
            hasher.update(block);
          }
          block = hasher.update(password).finalize(salt);
          hasher.reset();

          // Iterations
          for (var i = 1; i < iterations; i++) {
            block = hasher.finalize(block);
            hasher.reset();
          }
          derivedKey.concat(block);
        }
        derivedKey.sigBytes = keySize * 4;
        return derivedKey;
      }
    });

    /**
     * Derives a key from a password.
     *
     * @param {WordArray|string} password The password.
     * @param {WordArray|string} salt A salt.
     * @param {Object} cfg (Optional) The configuration options to use for this computation.
     *
     * @return {WordArray} The derived key.
     *
     * @static
     *
     * @example
     *
     *     var key = CryptoJS.EvpKDF(password, salt);
     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8 });
     *     var key = CryptoJS.EvpKDF(password, salt, { keySize: 8, iterations: 1000 });
     */
    C.EvpKDF = function (password, salt, cfg) {
      return EvpKDF.create(cfg).compute(password, salt);
    };
  })();
  return CryptoJS.EvpKDF;
});

/***/ }),

/***/ 73948:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(14032);
__webpack_require__(57114);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function (undefined) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var CipherParams = C_lib.CipherParams;
    var C_enc = C.enc;
    var Hex = C_enc.Hex;
    var C_format = C.format;
    var HexFormatter = C_format.Hex = {
      /**
       * Converts the ciphertext of a cipher params object to a hexadecimally encoded string.
       *
       * @param {CipherParams} cipherParams The cipher params object.
       *
       * @return {string} The hexadecimally encoded string.
       *
       * @static
       *
       * @example
       *
       *     var hexString = CryptoJS.format.Hex.stringify(cipherParams);
       */
      stringify: function stringify(cipherParams) {
        return cipherParams.ciphertext.toString(Hex);
      },
      /**
       * Converts a hexadecimally encoded ciphertext string to a cipher params object.
       *
       * @param {string} input The hexadecimally encoded string.
       *
       * @return {CipherParams} The cipher params object.
       *
       * @static
       *
       * @example
       *
       *     var cipherParams = CryptoJS.format.Hex.parse(hexString);
       */
      parse: function parse(input) {
        var ciphertext = Hex.parse(input);
        return CipherParams.create({
          ciphertext: ciphertext
        });
      }
    };
  })();
  return CryptoJS.format.Hex;
});

/***/ }),

/***/ 4420:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var C_enc = C.enc;
    var Utf8 = C_enc.Utf8;
    var C_algo = C.algo;

    /**
     * HMAC algorithm.
     */
    var HMAC = C_algo.HMAC = Base.extend({
      /**
       * Initializes a newly created HMAC.
       *
       * @param {Hasher} hasher The hash algorithm to use.
       * @param {WordArray|string} key The secret key.
       *
       * @example
       *
       *     var hmacHasher = CryptoJS.algo.HMAC.create(CryptoJS.algo.SHA256, key);
       */
      init: function init(hasher, key) {
        // Init hasher
        hasher = this._hasher = new hasher.init();

        // Convert string to WordArray, else assume WordArray already
        if (typeof key == 'string') {
          key = Utf8.parse(key);
        }

        // Shortcuts
        var hasherBlockSize = hasher.blockSize;
        var hasherBlockSizeBytes = hasherBlockSize * 4;

        // Allow arbitrary length keys
        if (key.sigBytes > hasherBlockSizeBytes) {
          key = hasher.finalize(key);
        }

        // Clamp excess bits
        key.clamp();

        // Clone key for inner and outer pads
        var oKey = this._oKey = key.clone();
        var iKey = this._iKey = key.clone();

        // Shortcuts
        var oKeyWords = oKey.words;
        var iKeyWords = iKey.words;

        // XOR keys with pad constants
        for (var i = 0; i < hasherBlockSize; i++) {
          oKeyWords[i] ^= 0x5c5c5c5c;
          iKeyWords[i] ^= 0x36363636;
        }
        oKey.sigBytes = iKey.sigBytes = hasherBlockSizeBytes;

        // Set initial values
        this.reset();
      },
      /**
       * Resets this HMAC to its initial state.
       *
       * @example
       *
       *     hmacHasher.reset();
       */
      reset: function reset() {
        // Shortcut
        var hasher = this._hasher;

        // Reset
        hasher.reset();
        hasher.update(this._iKey);
      },
      /**
       * Updates this HMAC with a message.
       *
       * @param {WordArray|string} messageUpdate The message to append.
       *
       * @return {HMAC} This HMAC instance.
       *
       * @example
       *
       *     hmacHasher.update('message');
       *     hmacHasher.update(wordArray);
       */
      update: function update(messageUpdate) {
        this._hasher.update(messageUpdate);

        // Chainable
        return this;
      },
      /**
       * Finalizes the HMAC computation.
       * Note that the finalize operation is effectively a destructive, read-once operation.
       *
       * @param {WordArray|string} messageUpdate (Optional) A final message update.
       *
       * @return {WordArray} The HMAC.
       *
       * @example
       *
       *     var hmac = hmacHasher.finalize();
       *     var hmac = hmacHasher.finalize('message');
       *     var hmac = hmacHasher.finalize(wordArray);
       */
      finalize: function finalize(messageUpdate) {
        // Shortcut
        var hasher = this._hasher;

        // Compute HMAC
        var innerHash = hasher.finalize(messageUpdate);
        hasher.reset();
        var hmac = hasher.finalize(this._oKey.clone().concat(innerHash));
        return hmac;
      }
    });
  })();
});

/***/ }),

/***/ 94119:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(18909), __webpack_require__(14287), __webpack_require__(2256), __webpack_require__(66947), __webpack_require__(16190), __webpack_require__(68319), __webpack_require__(37146), __webpack_require__(66664), __webpack_require__(11905), __webpack_require__(51965), __webpack_require__(27772), __webpack_require__(16720), __webpack_require__(1239), __webpack_require__(4420), __webpack_require__(44130), __webpack_require__(82747), __webpack_require__(51270), __webpack_require__(62256), __webpack_require__(48306), __webpack_require__(65881), __webpack_require__(6564), __webpack_require__(29819), __webpack_require__(28164), __webpack_require__(91780), __webpack_require__(14623), __webpack_require__(85628), __webpack_require__(61807), __webpack_require__(73948), __webpack_require__(22956), __webpack_require__(24107), __webpack_require__(95086), __webpack_require__(6819), __webpack_require__(75891), __webpack_require__(73517));
  } else {}
})(void 0, function (CryptoJS) {
  return CryptoJS;
});

/***/ }),

/***/ 14287:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(81755);
__webpack_require__(8953);
__webpack_require__(14032);
__webpack_require__(3131);
__webpack_require__(90868);
__webpack_require__(65553);
__webpack_require__(75626);
__webpack_require__(95756);
__webpack_require__(56912);
__webpack_require__(58099);
__webpack_require__(47969);
__webpack_require__(59735);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Check if typed arrays are supported
    if (typeof ArrayBuffer != 'function') {
      return;
    }

    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;

    // Reference original init
    var superInit = WordArray.init;

    // Augment WordArray.init to handle typed arrays
    var subInit = WordArray.init = function (typedArray) {
      // Convert buffers to uint8
      if (typedArray instanceof ArrayBuffer) {
        typedArray = new Uint8Array(typedArray);
      }

      // Convert other array views to uint8
      if (typedArray instanceof Int8Array || typeof Uint8ClampedArray !== "undefined" && typedArray instanceof Uint8ClampedArray || typedArray instanceof Int16Array || typedArray instanceof Uint16Array || typedArray instanceof Int32Array || typedArray instanceof Uint32Array || typedArray instanceof Float32Array || typedArray instanceof Float64Array) {
        typedArray = new Uint8Array(typedArray.buffer, typedArray.byteOffset, typedArray.byteLength);
      }

      // Handle Uint8Array
      if (typedArray instanceof Uint8Array) {
        // Shortcut
        var typedArrayByteLength = typedArray.byteLength;

        // Extract bytes
        var words = [];
        for (var i = 0; i < typedArrayByteLength; i++) {
          words[i >>> 2] |= typedArray[i] << 24 - i % 4 * 8;
        }

        // Initialize this word array
        superInit.call(this, words, typedArrayByteLength);
      } else {
        // Else call normal init
        superInit.apply(this, arguments);
      }
    };
    subInit.prototype = WordArray;
  })();
  return CryptoJS.lib.WordArray;
});

/***/ }),

/***/ 68319:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Constants table
    var T = [];

    // Compute constants
    (function () {
      for (var i = 0; i < 64; i++) {
        T[i] = Math.abs(Math.sin(i + 1)) * 0x100000000 | 0;
      }
    })();

    /**
     * MD5 hash algorithm.
     */
    var MD5 = C_algo.MD5 = Hasher.extend({
      _doReset: function _doReset() {
        this._hash = new WordArray.init([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476]);
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Swap endian
        for (var i = 0; i < 16; i++) {
          // Shortcuts
          var offset_i = offset + i;
          var M_offset_i = M[offset_i];
          M[offset_i] = (M_offset_i << 8 | M_offset_i >>> 24) & 0x00ff00ff | (M_offset_i << 24 | M_offset_i >>> 8) & 0xff00ff00;
        }

        // Shortcuts
        var H = this._hash.words;
        var M_offset_0 = M[offset + 0];
        var M_offset_1 = M[offset + 1];
        var M_offset_2 = M[offset + 2];
        var M_offset_3 = M[offset + 3];
        var M_offset_4 = M[offset + 4];
        var M_offset_5 = M[offset + 5];
        var M_offset_6 = M[offset + 6];
        var M_offset_7 = M[offset + 7];
        var M_offset_8 = M[offset + 8];
        var M_offset_9 = M[offset + 9];
        var M_offset_10 = M[offset + 10];
        var M_offset_11 = M[offset + 11];
        var M_offset_12 = M[offset + 12];
        var M_offset_13 = M[offset + 13];
        var M_offset_14 = M[offset + 14];
        var M_offset_15 = M[offset + 15];

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];

        // Computation
        a = FF(a, b, c, d, M_offset_0, 7, T[0]);
        d = FF(d, a, b, c, M_offset_1, 12, T[1]);
        c = FF(c, d, a, b, M_offset_2, 17, T[2]);
        b = FF(b, c, d, a, M_offset_3, 22, T[3]);
        a = FF(a, b, c, d, M_offset_4, 7, T[4]);
        d = FF(d, a, b, c, M_offset_5, 12, T[5]);
        c = FF(c, d, a, b, M_offset_6, 17, T[6]);
        b = FF(b, c, d, a, M_offset_7, 22, T[7]);
        a = FF(a, b, c, d, M_offset_8, 7, T[8]);
        d = FF(d, a, b, c, M_offset_9, 12, T[9]);
        c = FF(c, d, a, b, M_offset_10, 17, T[10]);
        b = FF(b, c, d, a, M_offset_11, 22, T[11]);
        a = FF(a, b, c, d, M_offset_12, 7, T[12]);
        d = FF(d, a, b, c, M_offset_13, 12, T[13]);
        c = FF(c, d, a, b, M_offset_14, 17, T[14]);
        b = FF(b, c, d, a, M_offset_15, 22, T[15]);
        a = GG(a, b, c, d, M_offset_1, 5, T[16]);
        d = GG(d, a, b, c, M_offset_6, 9, T[17]);
        c = GG(c, d, a, b, M_offset_11, 14, T[18]);
        b = GG(b, c, d, a, M_offset_0, 20, T[19]);
        a = GG(a, b, c, d, M_offset_5, 5, T[20]);
        d = GG(d, a, b, c, M_offset_10, 9, T[21]);
        c = GG(c, d, a, b, M_offset_15, 14, T[22]);
        b = GG(b, c, d, a, M_offset_4, 20, T[23]);
        a = GG(a, b, c, d, M_offset_9, 5, T[24]);
        d = GG(d, a, b, c, M_offset_14, 9, T[25]);
        c = GG(c, d, a, b, M_offset_3, 14, T[26]);
        b = GG(b, c, d, a, M_offset_8, 20, T[27]);
        a = GG(a, b, c, d, M_offset_13, 5, T[28]);
        d = GG(d, a, b, c, M_offset_2, 9, T[29]);
        c = GG(c, d, a, b, M_offset_7, 14, T[30]);
        b = GG(b, c, d, a, M_offset_12, 20, T[31]);
        a = HH(a, b, c, d, M_offset_5, 4, T[32]);
        d = HH(d, a, b, c, M_offset_8, 11, T[33]);
        c = HH(c, d, a, b, M_offset_11, 16, T[34]);
        b = HH(b, c, d, a, M_offset_14, 23, T[35]);
        a = HH(a, b, c, d, M_offset_1, 4, T[36]);
        d = HH(d, a, b, c, M_offset_4, 11, T[37]);
        c = HH(c, d, a, b, M_offset_7, 16, T[38]);
        b = HH(b, c, d, a, M_offset_10, 23, T[39]);
        a = HH(a, b, c, d, M_offset_13, 4, T[40]);
        d = HH(d, a, b, c, M_offset_0, 11, T[41]);
        c = HH(c, d, a, b, M_offset_3, 16, T[42]);
        b = HH(b, c, d, a, M_offset_6, 23, T[43]);
        a = HH(a, b, c, d, M_offset_9, 4, T[44]);
        d = HH(d, a, b, c, M_offset_12, 11, T[45]);
        c = HH(c, d, a, b, M_offset_15, 16, T[46]);
        b = HH(b, c, d, a, M_offset_2, 23, T[47]);
        a = II(a, b, c, d, M_offset_0, 6, T[48]);
        d = II(d, a, b, c, M_offset_7, 10, T[49]);
        c = II(c, d, a, b, M_offset_14, 15, T[50]);
        b = II(b, c, d, a, M_offset_5, 21, T[51]);
        a = II(a, b, c, d, M_offset_12, 6, T[52]);
        d = II(d, a, b, c, M_offset_3, 10, T[53]);
        c = II(c, d, a, b, M_offset_10, 15, T[54]);
        b = II(b, c, d, a, M_offset_1, 21, T[55]);
        a = II(a, b, c, d, M_offset_8, 6, T[56]);
        d = II(d, a, b, c, M_offset_15, 10, T[57]);
        c = II(c, d, a, b, M_offset_6, 15, T[58]);
        b = II(b, c, d, a, M_offset_13, 21, T[59]);
        a = II(a, b, c, d, M_offset_4, 6, T[60]);
        d = II(d, a, b, c, M_offset_11, 10, T[61]);
        c = II(c, d, a, b, M_offset_2, 15, T[62]);
        b = II(b, c, d, a, M_offset_9, 21, T[63]);

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        var nBitsTotalH = Math.floor(nBitsTotal / 0x100000000);
        var nBitsTotalL = nBitsTotal;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = (nBitsTotalH << 8 | nBitsTotalH >>> 24) & 0x00ff00ff | (nBitsTotalH << 24 | nBitsTotalH >>> 8) & 0xff00ff00;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = (nBitsTotalL << 8 | nBitsTotalL >>> 24) & 0x00ff00ff | (nBitsTotalL << 24 | nBitsTotalL >>> 8) & 0xff00ff00;
        data.sigBytes = (dataWords.length + 1) * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var hash = this._hash;
        var H = hash.words;

        // Swap endian
        for (var i = 0; i < 4; i++) {
          // Shortcut
          var H_i = H[i];
          H[i] = (H_i << 8 | H_i >>> 24) & 0x00ff00ff | (H_i << 24 | H_i >>> 8) & 0xff00ff00;
        }

        // Return final computed hash
        return hash;
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });
    function FF(a, b, c, d, x, s, t) {
      var n = a + (b & c | ~b & d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function GG(a, b, c, d, x, s, t) {
      var n = a + (b & d | c & ~d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function HH(a, b, c, d, x, s, t) {
      var n = a + (b ^ c ^ d) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }
    function II(a, b, c, d, x, s, t) {
      var n = a + (c ^ (b | ~d)) + x + t;
      return (n << s | n >>> 32 - s) + b;
    }

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.MD5('message');
     *     var hash = CryptoJS.MD5(wordArray);
     */
    C.MD5 = Hasher._createHelper(MD5);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacMD5(message, key);
     */
    C.HmacMD5 = Hasher._createHmacHelper(MD5);
  })(Math);
  return CryptoJS.MD5;
});

/***/ }),

/***/ 62256:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Cipher Feedback block mode.
   */
  CryptoJS.mode.CFB = function () {
    var CFB = CryptoJS.lib.BlockCipherMode.extend();
    CFB.Encryptor = CFB.extend({
      processBlock: function processBlock(words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

        // Remember this block to use with next block
        this._prevBlock = words.slice(offset, offset + blockSize);
      }
    });
    CFB.Decryptor = CFB.extend({
      processBlock: function processBlock(words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;

        // Remember this block to use with next block
        var thisBlock = words.slice(offset, offset + blockSize);
        generateKeystreamAndEncrypt.call(this, words, offset, blockSize, cipher);

        // This block becomes the previous block
        this._prevBlock = thisBlock;
      }
    });
    function generateKeystreamAndEncrypt(words, offset, blockSize, cipher) {
      var keystream;

      // Shortcut
      var iv = this._iv;

      // Generate keystream
      if (iv) {
        keystream = iv.slice(0);

        // Remove IV for subsequent blocks
        this._iv = undefined;
      } else {
        keystream = this._prevBlock;
      }
      cipher.encryptBlock(keystream, 0);

      // Encrypt
      for (var i = 0; i < blockSize; i++) {
        words[offset + i] ^= keystream[i];
      }
    }
    return CFB;
  }();
  return CryptoJS.mode.CFB;
});

/***/ }),

/***/ 65881:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /** @preserve
   * Counter block mode compatible with  Dr Brian Gladman fileenc.c
   * derived from CryptoJS.mode.CTR
   * Jan Hruby jhruby.web@gmail.com
   */
  CryptoJS.mode.CTRGladman = function () {
    var CTRGladman = CryptoJS.lib.BlockCipherMode.extend();
    function incWord(word) {
      if ((word >> 24 & 0xff) === 0xff) {
        //overflow
        var b1 = word >> 16 & 0xff;
        var b2 = word >> 8 & 0xff;
        var b3 = word & 0xff;
        if (b1 === 0xff)
          // overflow b1
          {
            b1 = 0;
            if (b2 === 0xff) {
              b2 = 0;
              if (b3 === 0xff) {
                b3 = 0;
              } else {
                ++b3;
              }
            } else {
              ++b2;
            }
          } else {
          ++b1;
        }
        word = 0;
        word += b1 << 16;
        word += b2 << 8;
        word += b3;
      } else {
        word += 0x01 << 24;
      }
      return word;
    }
    function incCounter(counter) {
      if ((counter[0] = incWord(counter[0])) === 0) {
        // encr_data in fileenc.c from  Dr Brian Gladman's counts only with DWORD j < 8
        counter[1] = incWord(counter[1]);
      }
      return counter;
    }
    var Encryptor = CTRGladman.Encryptor = CTRGladman.extend({
      processBlock: function processBlock(words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var counter = this._counter;

        // Generate keystream
        if (iv) {
          counter = this._counter = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        incCounter(counter);
        var keystream = counter.slice(0);
        cipher.encryptBlock(keystream, 0);

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    CTRGladman.Decryptor = Encryptor;
    return CTRGladman;
  }();
  return CryptoJS.mode.CTRGladman;
});

/***/ }),

/***/ 48306:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Counter block mode.
   */
  CryptoJS.mode.CTR = function () {
    var CTR = CryptoJS.lib.BlockCipherMode.extend();
    var Encryptor = CTR.Encryptor = CTR.extend({
      processBlock: function processBlock(words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var counter = this._counter;

        // Generate keystream
        if (iv) {
          counter = this._counter = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        var keystream = counter.slice(0);
        cipher.encryptBlock(keystream, 0);

        // Increment counter
        counter[blockSize - 1] = counter[blockSize - 1] + 1 | 0;

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    CTR.Decryptor = Encryptor;
    return CTR;
  }();
  return CryptoJS.mode.CTR;
});

/***/ }),

/***/ 29819:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Electronic Codebook block mode.
   */
  CryptoJS.mode.ECB = function () {
    var ECB = CryptoJS.lib.BlockCipherMode.extend();
    ECB.Encryptor = ECB.extend({
      processBlock: function processBlock(words, offset) {
        this._cipher.encryptBlock(words, offset);
      }
    });
    ECB.Decryptor = ECB.extend({
      processBlock: function processBlock(words, offset) {
        this._cipher.decryptBlock(words, offset);
      }
    });
    return ECB;
  }();
  return CryptoJS.mode.ECB;
});

/***/ }),

/***/ 6564:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Output Feedback block mode.
   */
  CryptoJS.mode.OFB = function () {
    var OFB = CryptoJS.lib.BlockCipherMode.extend();
    var Encryptor = OFB.Encryptor = OFB.extend({
      processBlock: function processBlock(words, offset) {
        // Shortcuts
        var cipher = this._cipher;
        var blockSize = cipher.blockSize;
        var iv = this._iv;
        var keystream = this._keystream;

        // Generate keystream
        if (iv) {
          keystream = this._keystream = iv.slice(0);

          // Remove IV for subsequent blocks
          this._iv = undefined;
        }
        cipher.encryptBlock(keystream, 0);

        // Encrypt
        for (var i = 0; i < blockSize; i++) {
          words[offset + i] ^= keystream[i];
        }
      }
    });
    OFB.Decryptor = Encryptor;
    return OFB;
  }();
  return CryptoJS.mode.OFB;
});

/***/ }),

/***/ 28164:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * ANSI X.923 padding strategy.
   */
  CryptoJS.pad.AnsiX923 = {
    pad: function pad(data, blockSize) {
      // Shortcuts
      var dataSigBytes = data.sigBytes;
      var blockSizeBytes = blockSize * 4;

      // Count padding bytes
      var nPaddingBytes = blockSizeBytes - dataSigBytes % blockSizeBytes;

      // Compute last byte position
      var lastBytePos = dataSigBytes + nPaddingBytes - 1;

      // Pad
      data.clamp();
      data.words[lastBytePos >>> 2] |= nPaddingBytes << 24 - lastBytePos % 4 * 8;
      data.sigBytes += nPaddingBytes;
    },
    unpad: function unpad(data) {
      // Get number of padding bytes from last byte
      var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

      // Remove padding
      data.sigBytes -= nPaddingBytes;
    }
  };
  return CryptoJS.pad.Ansix923;
});

/***/ }),

/***/ 91780:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * ISO 10126 padding strategy.
   */
  CryptoJS.pad.Iso10126 = {
    pad: function pad(data, blockSize) {
      // Shortcut
      var blockSizeBytes = blockSize * 4;

      // Count padding bytes
      var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes;

      // Pad
      data.concat(CryptoJS.lib.WordArray.random(nPaddingBytes - 1)).concat(CryptoJS.lib.WordArray.create([nPaddingBytes << 24], 1));
    },
    unpad: function unpad(data) {
      // Get number of padding bytes from last byte
      var nPaddingBytes = data.words[data.sigBytes - 1 >>> 2] & 0xff;

      // Remove padding
      data.sigBytes -= nPaddingBytes;
    }
  };
  return CryptoJS.pad.Iso10126;
});

/***/ }),

/***/ 14623:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * ISO/IEC 9797-1 Padding Method 2.
   */
  CryptoJS.pad.Iso97971 = {
    pad: function pad(data, blockSize) {
      // Add 0x80 byte
      data.concat(CryptoJS.lib.WordArray.create([0x80000000], 1));

      // Zero pad the rest
      CryptoJS.pad.ZeroPadding.pad(data, blockSize);
    },
    unpad: function unpad(data) {
      // Remove zero padding
      CryptoJS.pad.ZeroPadding.unpad(data);

      // Remove one more byte -- the 0x80 byte
      data.sigBytes--;
    }
  };
  return CryptoJS.pad.Iso97971;
});

/***/ }),

/***/ 61807:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * A noop padding strategy.
   */
  CryptoJS.pad.NoPadding = {
    pad: function pad() {},
    unpad: function unpad() {}
  };
  return CryptoJS.pad.NoPadding;
});

/***/ }),

/***/ 85628:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  /**
   * Zero padding strategy.
   */
  CryptoJS.pad.ZeroPadding = {
    pad: function pad(data, blockSize) {
      // Shortcut
      var blockSizeBytes = blockSize * 4;

      // Pad
      data.clamp();
      data.sigBytes += blockSizeBytes - (data.sigBytes % blockSizeBytes || blockSizeBytes);
    },
    unpad: function unpad(data) {
      // Shortcut
      var dataWords = data.words;

      // Unpad
      var i = data.sigBytes - 1;
      for (var i = data.sigBytes - 1; i >= 0; i--) {
        if (dataWords[i >>> 2] >>> 24 - i % 4 * 8 & 0xff) {
          data.sigBytes = i + 1;
          break;
        }
      }
    }
  };
  return CryptoJS.pad.ZeroPadding;
});

/***/ }),

/***/ 44130:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(39081);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66664), __webpack_require__(4420));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var SHA256 = C_algo.SHA256;
    var HMAC = C_algo.HMAC;

    /**
     * Password-Based Key Derivation Function 2 algorithm.
     */
    var PBKDF2 = C_algo.PBKDF2 = Base.extend({
      /**
       * Configuration options.
       *
       * @property {number} keySize The key size in words to generate. Default: 4 (128 bits)
       * @property {Hasher} hasher The hasher to use. Default: SHA256
       * @property {number} iterations The number of iterations to perform. Default: 250000
       */
      cfg: Base.extend({
        keySize: 128 / 32,
        hasher: SHA256,
        iterations: 250000
      }),
      /**
       * Initializes a newly created key derivation function.
       *
       * @param {Object} cfg (Optional) The configuration options to use for the derivation.
       *
       * @example
       *
       *     var kdf = CryptoJS.algo.PBKDF2.create();
       *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8 });
       *     var kdf = CryptoJS.algo.PBKDF2.create({ keySize: 8, iterations: 1000 });
       */
      init: function init(cfg) {
        this.cfg = this.cfg.extend(cfg);
      },
      /**
       * Computes the Password-Based Key Derivation Function 2.
       *
       * @param {WordArray|string} password The password.
       * @param {WordArray|string} salt A salt.
       *
       * @return {WordArray} The derived key.
       *
       * @example
       *
       *     var key = kdf.compute(password, salt);
       */
      compute: function compute(password, salt) {
        // Shortcut
        var cfg = this.cfg;

        // Init HMAC
        var hmac = HMAC.create(cfg.hasher, password);

        // Initial values
        var derivedKey = WordArray.create();
        var blockIndex = WordArray.create([0x00000001]);

        // Shortcuts
        var derivedKeyWords = derivedKey.words;
        var blockIndexWords = blockIndex.words;
        var keySize = cfg.keySize;
        var iterations = cfg.iterations;

        // Generate key
        while (derivedKeyWords.length < keySize) {
          var block = hmac.update(salt).finalize(blockIndex);
          hmac.reset();

          // Shortcuts
          var blockWords = block.words;
          var blockWordsLength = blockWords.length;

          // Iterations
          var intermediate = block;
          for (var i = 1; i < iterations; i++) {
            intermediate = hmac.finalize(intermediate);
            hmac.reset();

            // Shortcut
            var intermediateWords = intermediate.words;

            // XOR intermediate with block
            for (var j = 0; j < blockWordsLength; j++) {
              blockWords[j] ^= intermediateWords[j];
            }
          }
          derivedKey.concat(block);
          blockIndexWords[0]++;
        }
        derivedKey.sigBytes = keySize * 4;
        return derivedKey;
      }
    });

    /**
     * Computes the Password-Based Key Derivation Function 2.
     *
     * @param {WordArray|string} password The password.
     * @param {WordArray|string} salt A salt.
     * @param {Object} cfg (Optional) The configuration options to use for this computation.
     *
     * @return {WordArray} The derived key.
     *
     * @static
     *
     * @example
     *
     *     var key = CryptoJS.PBKDF2(password, salt);
     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8 });
     *     var key = CryptoJS.PBKDF2(password, salt, { keySize: 8, iterations: 1000 });
     */
    C.PBKDF2 = function (password, salt, cfg) {
      return PBKDF2.create(cfg).compute(password, salt);
    };
  })();
  return CryptoJS.PBKDF2;
});

/***/ }),

/***/ 75891:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    // Reusable objects
    var S = [];
    var C_ = [];
    var G = [];

    /**
     * Rabbit stream cipher algorithm.
     *
     * This is a legacy version that neglected to convert the key to little-endian.
     * This error doesn't affect the cipher's security,
     * but it does affect its compatibility with other implementations.
     */
    var RabbitLegacy = C_algo.RabbitLegacy = StreamCipher.extend({
      _doReset: function _doReset() {
        // Shortcuts
        var K = this._key.words;
        var iv = this.cfg.iv;

        // Generate initial state values
        var X = this._X = [K[0], K[3] << 16 | K[2] >>> 16, K[1], K[0] << 16 | K[3] >>> 16, K[2], K[1] << 16 | K[0] >>> 16, K[3], K[2] << 16 | K[1] >>> 16];

        // Generate initial counter values
        var C = this._C = [K[2] << 16 | K[2] >>> 16, K[0] & 0xffff0000 | K[1] & 0x0000ffff, K[3] << 16 | K[3] >>> 16, K[1] & 0xffff0000 | K[2] & 0x0000ffff, K[0] << 16 | K[0] >>> 16, K[2] & 0xffff0000 | K[3] & 0x0000ffff, K[1] << 16 | K[1] >>> 16, K[3] & 0xffff0000 | K[0] & 0x0000ffff];

        // Carry bit
        this._b = 0;

        // Iterate the system four times
        for (var i = 0; i < 4; i++) {
          nextState.call(this);
        }

        // Modify the counters
        for (var i = 0; i < 8; i++) {
          C[i] ^= X[i + 4 & 7];
        }

        // IV setup
        if (iv) {
          // Shortcuts
          var IV = iv.words;
          var IV_0 = IV[0];
          var IV_1 = IV[1];

          // Generate four subvectors
          var i0 = (IV_0 << 8 | IV_0 >>> 24) & 0x00ff00ff | (IV_0 << 24 | IV_0 >>> 8) & 0xff00ff00;
          var i2 = (IV_1 << 8 | IV_1 >>> 24) & 0x00ff00ff | (IV_1 << 24 | IV_1 >>> 8) & 0xff00ff00;
          var i1 = i0 >>> 16 | i2 & 0xffff0000;
          var i3 = i2 << 16 | i0 & 0x0000ffff;

          // Modify counter values
          C[0] ^= i0;
          C[1] ^= i1;
          C[2] ^= i2;
          C[3] ^= i3;
          C[4] ^= i0;
          C[5] ^= i1;
          C[6] ^= i2;
          C[7] ^= i3;

          // Iterate the system four times
          for (var i = 0; i < 4; i++) {
            nextState.call(this);
          }
        }
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcut
        var X = this._X;

        // Iterate the system
        nextState.call(this);

        // Generate four keystream words
        S[0] = X[0] ^ X[5] >>> 16 ^ X[3] << 16;
        S[1] = X[2] ^ X[7] >>> 16 ^ X[5] << 16;
        S[2] = X[4] ^ X[1] >>> 16 ^ X[7] << 16;
        S[3] = X[6] ^ X[3] >>> 16 ^ X[1] << 16;
        for (var i = 0; i < 4; i++) {
          // Swap endian
          S[i] = (S[i] << 8 | S[i] >>> 24) & 0x00ff00ff | (S[i] << 24 | S[i] >>> 8) & 0xff00ff00;

          // Encrypt
          M[offset + i] ^= S[i];
        }
      },
      blockSize: 128 / 32,
      ivSize: 64 / 32
    });
    function nextState() {
      // Shortcuts
      var X = this._X;
      var C = this._C;

      // Save old counter values
      for (var i = 0; i < 8; i++) {
        C_[i] = C[i];
      }

      // Calculate new counter values
      C[0] = C[0] + 0x4d34d34d + this._b | 0;
      C[1] = C[1] + 0xd34d34d3 + (C[0] >>> 0 < C_[0] >>> 0 ? 1 : 0) | 0;
      C[2] = C[2] + 0x34d34d34 + (C[1] >>> 0 < C_[1] >>> 0 ? 1 : 0) | 0;
      C[3] = C[3] + 0x4d34d34d + (C[2] >>> 0 < C_[2] >>> 0 ? 1 : 0) | 0;
      C[4] = C[4] + 0xd34d34d3 + (C[3] >>> 0 < C_[3] >>> 0 ? 1 : 0) | 0;
      C[5] = C[5] + 0x34d34d34 + (C[4] >>> 0 < C_[4] >>> 0 ? 1 : 0) | 0;
      C[6] = C[6] + 0x4d34d34d + (C[5] >>> 0 < C_[5] >>> 0 ? 1 : 0) | 0;
      C[7] = C[7] + 0xd34d34d3 + (C[6] >>> 0 < C_[6] >>> 0 ? 1 : 0) | 0;
      this._b = C[7] >>> 0 < C_[7] >>> 0 ? 1 : 0;

      // Calculate the g-values
      for (var i = 0; i < 8; i++) {
        var gx = X[i] + C[i];

        // Construct high and low argument for squaring
        var ga = gx & 0xffff;
        var gb = gx >>> 16;

        // Calculate high and low result of squaring
        var gh = ((ga * ga >>> 17) + ga * gb >>> 15) + gb * gb;
        var gl = ((gx & 0xffff0000) * gx | 0) + ((gx & 0x0000ffff) * gx | 0);

        // High XOR low
        G[i] = gh ^ gl;
      }

      // Calculate new state values
      X[0] = G[0] + (G[7] << 16 | G[7] >>> 16) + (G[6] << 16 | G[6] >>> 16) | 0;
      X[1] = G[1] + (G[0] << 8 | G[0] >>> 24) + G[7] | 0;
      X[2] = G[2] + (G[1] << 16 | G[1] >>> 16) + (G[0] << 16 | G[0] >>> 16) | 0;
      X[3] = G[3] + (G[2] << 8 | G[2] >>> 24) + G[1] | 0;
      X[4] = G[4] + (G[3] << 16 | G[3] >>> 16) + (G[2] << 16 | G[2] >>> 16) | 0;
      X[5] = G[5] + (G[4] << 8 | G[4] >>> 24) + G[3] | 0;
      X[6] = G[6] + (G[5] << 16 | G[5] >>> 16) + (G[4] << 16 | G[4] >>> 16) | 0;
      X[7] = G[7] + (G[6] << 8 | G[6] >>> 24) + G[5] | 0;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RabbitLegacy.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RabbitLegacy.decrypt(ciphertext, key, cfg);
     */
    C.RabbitLegacy = StreamCipher._createHelper(RabbitLegacy);
  })();
  return CryptoJS.RabbitLegacy;
});

/***/ }),

/***/ 6819:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    // Reusable objects
    var S = [];
    var C_ = [];
    var G = [];

    /**
     * Rabbit stream cipher algorithm
     */
    var Rabbit = C_algo.Rabbit = StreamCipher.extend({
      _doReset: function _doReset() {
        // Shortcuts
        var K = this._key.words;
        var iv = this.cfg.iv;

        // Swap endian
        for (var i = 0; i < 4; i++) {
          K[i] = (K[i] << 8 | K[i] >>> 24) & 0x00ff00ff | (K[i] << 24 | K[i] >>> 8) & 0xff00ff00;
        }

        // Generate initial state values
        var X = this._X = [K[0], K[3] << 16 | K[2] >>> 16, K[1], K[0] << 16 | K[3] >>> 16, K[2], K[1] << 16 | K[0] >>> 16, K[3], K[2] << 16 | K[1] >>> 16];

        // Generate initial counter values
        var C = this._C = [K[2] << 16 | K[2] >>> 16, K[0] & 0xffff0000 | K[1] & 0x0000ffff, K[3] << 16 | K[3] >>> 16, K[1] & 0xffff0000 | K[2] & 0x0000ffff, K[0] << 16 | K[0] >>> 16, K[2] & 0xffff0000 | K[3] & 0x0000ffff, K[1] << 16 | K[1] >>> 16, K[3] & 0xffff0000 | K[0] & 0x0000ffff];

        // Carry bit
        this._b = 0;

        // Iterate the system four times
        for (var i = 0; i < 4; i++) {
          nextState.call(this);
        }

        // Modify the counters
        for (var i = 0; i < 8; i++) {
          C[i] ^= X[i + 4 & 7];
        }

        // IV setup
        if (iv) {
          // Shortcuts
          var IV = iv.words;
          var IV_0 = IV[0];
          var IV_1 = IV[1];

          // Generate four subvectors
          var i0 = (IV_0 << 8 | IV_0 >>> 24) & 0x00ff00ff | (IV_0 << 24 | IV_0 >>> 8) & 0xff00ff00;
          var i2 = (IV_1 << 8 | IV_1 >>> 24) & 0x00ff00ff | (IV_1 << 24 | IV_1 >>> 8) & 0xff00ff00;
          var i1 = i0 >>> 16 | i2 & 0xffff0000;
          var i3 = i2 << 16 | i0 & 0x0000ffff;

          // Modify counter values
          C[0] ^= i0;
          C[1] ^= i1;
          C[2] ^= i2;
          C[3] ^= i3;
          C[4] ^= i0;
          C[5] ^= i1;
          C[6] ^= i2;
          C[7] ^= i3;

          // Iterate the system four times
          for (var i = 0; i < 4; i++) {
            nextState.call(this);
          }
        }
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcut
        var X = this._X;

        // Iterate the system
        nextState.call(this);

        // Generate four keystream words
        S[0] = X[0] ^ X[5] >>> 16 ^ X[3] << 16;
        S[1] = X[2] ^ X[7] >>> 16 ^ X[5] << 16;
        S[2] = X[4] ^ X[1] >>> 16 ^ X[7] << 16;
        S[3] = X[6] ^ X[3] >>> 16 ^ X[1] << 16;
        for (var i = 0; i < 4; i++) {
          // Swap endian
          S[i] = (S[i] << 8 | S[i] >>> 24) & 0x00ff00ff | (S[i] << 24 | S[i] >>> 8) & 0xff00ff00;

          // Encrypt
          M[offset + i] ^= S[i];
        }
      },
      blockSize: 128 / 32,
      ivSize: 64 / 32
    });
    function nextState() {
      // Shortcuts
      var X = this._X;
      var C = this._C;

      // Save old counter values
      for (var i = 0; i < 8; i++) {
        C_[i] = C[i];
      }

      // Calculate new counter values
      C[0] = C[0] + 0x4d34d34d + this._b | 0;
      C[1] = C[1] + 0xd34d34d3 + (C[0] >>> 0 < C_[0] >>> 0 ? 1 : 0) | 0;
      C[2] = C[2] + 0x34d34d34 + (C[1] >>> 0 < C_[1] >>> 0 ? 1 : 0) | 0;
      C[3] = C[3] + 0x4d34d34d + (C[2] >>> 0 < C_[2] >>> 0 ? 1 : 0) | 0;
      C[4] = C[4] + 0xd34d34d3 + (C[3] >>> 0 < C_[3] >>> 0 ? 1 : 0) | 0;
      C[5] = C[5] + 0x34d34d34 + (C[4] >>> 0 < C_[4] >>> 0 ? 1 : 0) | 0;
      C[6] = C[6] + 0x4d34d34d + (C[5] >>> 0 < C_[5] >>> 0 ? 1 : 0) | 0;
      C[7] = C[7] + 0xd34d34d3 + (C[6] >>> 0 < C_[6] >>> 0 ? 1 : 0) | 0;
      this._b = C[7] >>> 0 < C_[7] >>> 0 ? 1 : 0;

      // Calculate the g-values
      for (var i = 0; i < 8; i++) {
        var gx = X[i] + C[i];

        // Construct high and low argument for squaring
        var ga = gx & 0xffff;
        var gb = gx >>> 16;

        // Calculate high and low result of squaring
        var gh = ((ga * ga >>> 17) + ga * gb >>> 15) + gb * gb;
        var gl = ((gx & 0xffff0000) * gx | 0) + ((gx & 0x0000ffff) * gx | 0);

        // High XOR low
        G[i] = gh ^ gl;
      }

      // Calculate new state values
      X[0] = G[0] + (G[7] << 16 | G[7] >>> 16) + (G[6] << 16 | G[6] >>> 16) | 0;
      X[1] = G[1] + (G[0] << 8 | G[0] >>> 24) + G[7] | 0;
      X[2] = G[2] + (G[1] << 16 | G[1] >>> 16) + (G[0] << 16 | G[0] >>> 16) | 0;
      X[3] = G[3] + (G[2] << 8 | G[2] >>> 24) + G[1] | 0;
      X[4] = G[4] + (G[3] << 16 | G[3] >>> 16) + (G[2] << 16 | G[2] >>> 16) | 0;
      X[5] = G[5] + (G[4] << 8 | G[4] >>> 24) + G[3] | 0;
      X[6] = G[6] + (G[5] << 16 | G[5] >>> 16) + (G[4] << 16 | G[4] >>> 16) | 0;
      X[7] = G[7] + (G[6] << 8 | G[6] >>> 24) + G[5] | 0;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.Rabbit.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.Rabbit.decrypt(ciphertext, key, cfg);
     */
    C.Rabbit = StreamCipher._createHelper(Rabbit);
  })();
  return CryptoJS.Rabbit;
});

/***/ }),

/***/ 95086:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(14032);
__webpack_require__(68067);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var StreamCipher = C_lib.StreamCipher;
    var C_algo = C.algo;

    /**
     * RC4 stream cipher algorithm.
     */
    var RC4 = C_algo.RC4 = StreamCipher.extend({
      _doReset: function _doReset() {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;
        var keySigBytes = key.sigBytes;

        // Init sbox
        var S = this._S = [];
        for (var i = 0; i < 256; i++) {
          S[i] = i;
        }

        // Key setup
        for (var i = 0, j = 0; i < 256; i++) {
          var keyByteIndex = i % keySigBytes;
          var keyByte = keyWords[keyByteIndex >>> 2] >>> 24 - keyByteIndex % 4 * 8 & 0xff;
          j = (j + S[i] + keyByte) % 256;

          // Swap
          var t = S[i];
          S[i] = S[j];
          S[j] = t;
        }

        // Counters
        this._i = this._j = 0;
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        M[offset] ^= generateKeystreamWord.call(this);
      },
      keySize: 256 / 32,
      ivSize: 0
    });
    function generateKeystreamWord() {
      // Shortcuts
      var S = this._S;
      var i = this._i;
      var j = this._j;

      // Generate keystream word
      var keystreamWord = 0;
      for (var n = 0; n < 4; n++) {
        i = (i + 1) % 256;
        j = (j + S[i]) % 256;

        // Swap
        var t = S[i];
        S[i] = S[j];
        S[j] = t;
        keystreamWord |= S[(S[i] + S[j]) % 256] << 24 - n * 8;
      }

      // Update counters
      this._i = i;
      this._j = j;
      return keystreamWord;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RC4.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RC4.decrypt(ciphertext, key, cfg);
     */
    C.RC4 = StreamCipher._createHelper(RC4);

    /**
     * Modified RC4 stream cipher algorithm.
     */
    var RC4Drop = C_algo.RC4Drop = RC4.extend({
      /**
       * Configuration options.
       *
       * @property {number} drop The number of keystream words to drop. Default 192
       */
      cfg: RC4.cfg.extend({
        drop: 192
      }),
      _doReset: function _doReset() {
        RC4._doReset.call(this);

        // Drop
        for (var i = this.cfg.drop; i > 0; i--) {
          generateKeystreamWord.call(this);
        }
      }
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.RC4Drop.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.RC4Drop.decrypt(ciphertext, key, cfg);
     */
    C.RC4Drop = StreamCipher._createHelper(RC4Drop);
  })();
  return CryptoJS.RC4;
});

/***/ }),

/***/ 1239:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  /** @preserve
  (c) 2012 by Cédric Mesnil. All rights reserved.
  	Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
  	    - Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
      - Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
  	THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
  */

  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Constants table
    var _zl = WordArray.create([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 7, 4, 13, 1, 10, 6, 15, 3, 12, 0, 9, 5, 2, 14, 11, 8, 3, 10, 14, 4, 9, 15, 8, 1, 2, 7, 0, 6, 13, 11, 5, 12, 1, 9, 11, 10, 0, 8, 12, 4, 13, 3, 7, 15, 14, 5, 6, 2, 4, 0, 5, 9, 7, 12, 2, 10, 14, 1, 3, 8, 11, 6, 15, 13]);
    var _zr = WordArray.create([5, 14, 7, 0, 9, 2, 11, 4, 13, 6, 15, 8, 1, 10, 3, 12, 6, 11, 3, 7, 0, 13, 5, 10, 14, 15, 8, 12, 4, 9, 1, 2, 15, 5, 1, 3, 7, 14, 6, 9, 11, 8, 12, 2, 10, 0, 4, 13, 8, 6, 4, 1, 3, 11, 15, 0, 5, 12, 2, 13, 9, 7, 10, 14, 12, 15, 10, 4, 1, 5, 8, 7, 6, 2, 13, 14, 0, 3, 9, 11]);
    var _sl = WordArray.create([11, 14, 15, 12, 5, 8, 7, 9, 11, 13, 14, 15, 6, 7, 9, 8, 7, 6, 8, 13, 11, 9, 7, 15, 7, 12, 15, 9, 11, 7, 13, 12, 11, 13, 6, 7, 14, 9, 13, 15, 14, 8, 13, 6, 5, 12, 7, 5, 11, 12, 14, 15, 14, 15, 9, 8, 9, 14, 5, 6, 8, 6, 5, 12, 9, 15, 5, 11, 6, 8, 13, 12, 5, 12, 13, 14, 11, 8, 5, 6]);
    var _sr = WordArray.create([8, 9, 9, 11, 13, 15, 15, 5, 7, 7, 8, 11, 14, 14, 12, 6, 9, 13, 15, 7, 12, 8, 9, 11, 7, 7, 12, 7, 6, 15, 13, 11, 9, 7, 15, 11, 8, 6, 6, 14, 12, 13, 5, 14, 13, 13, 7, 5, 15, 5, 8, 11, 14, 14, 6, 14, 6, 9, 12, 9, 12, 5, 15, 8, 8, 5, 12, 9, 12, 5, 14, 6, 8, 13, 6, 5, 15, 13, 11, 11]);
    var _hl = WordArray.create([0x00000000, 0x5A827999, 0x6ED9EBA1, 0x8F1BBCDC, 0xA953FD4E]);
    var _hr = WordArray.create([0x50A28BE6, 0x5C4DD124, 0x6D703EF3, 0x7A6D76E9, 0x00000000]);

    /**
     * RIPEMD160 hash algorithm.
     */
    var RIPEMD160 = C_algo.RIPEMD160 = Hasher.extend({
      _doReset: function _doReset() {
        this._hash = WordArray.create([0x67452301, 0xEFCDAB89, 0x98BADCFE, 0x10325476, 0xC3D2E1F0]);
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Swap endian
        for (var i = 0; i < 16; i++) {
          // Shortcuts
          var offset_i = offset + i;
          var M_offset_i = M[offset_i];

          // Swap
          M[offset_i] = (M_offset_i << 8 | M_offset_i >>> 24) & 0x00ff00ff | (M_offset_i << 24 | M_offset_i >>> 8) & 0xff00ff00;
        }
        // Shortcut
        var H = this._hash.words;
        var hl = _hl.words;
        var hr = _hr.words;
        var zl = _zl.words;
        var zr = _zr.words;
        var sl = _sl.words;
        var sr = _sr.words;

        // Working variables
        var al, bl, cl, dl, el;
        var ar, br, cr, dr, er;
        ar = al = H[0];
        br = bl = H[1];
        cr = cl = H[2];
        dr = dl = H[3];
        er = el = H[4];
        // Computation
        var t;
        for (var i = 0; i < 80; i += 1) {
          t = al + M[offset + zl[i]] | 0;
          if (i < 16) {
            t += f1(bl, cl, dl) + hl[0];
          } else if (i < 32) {
            t += f2(bl, cl, dl) + hl[1];
          } else if (i < 48) {
            t += f3(bl, cl, dl) + hl[2];
          } else if (i < 64) {
            t += f4(bl, cl, dl) + hl[3];
          } else {
            // if (i<80) {
            t += f5(bl, cl, dl) + hl[4];
          }
          t = t | 0;
          t = rotl(t, sl[i]);
          t = t + el | 0;
          al = el;
          el = dl;
          dl = rotl(cl, 10);
          cl = bl;
          bl = t;
          t = ar + M[offset + zr[i]] | 0;
          if (i < 16) {
            t += f5(br, cr, dr) + hr[0];
          } else if (i < 32) {
            t += f4(br, cr, dr) + hr[1];
          } else if (i < 48) {
            t += f3(br, cr, dr) + hr[2];
          } else if (i < 64) {
            t += f2(br, cr, dr) + hr[3];
          } else {
            // if (i<80) {
            t += f1(br, cr, dr) + hr[4];
          }
          t = t | 0;
          t = rotl(t, sr[i]);
          t = t + er | 0;
          ar = er;
          er = dr;
          dr = rotl(cr, 10);
          cr = br;
          br = t;
        }
        // Intermediate hash value
        t = H[1] + cl + dr | 0;
        H[1] = H[2] + dl + er | 0;
        H[2] = H[3] + el + ar | 0;
        H[3] = H[4] + al + br | 0;
        H[4] = H[0] + bl + cr | 0;
        H[0] = t;
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = (nBitsTotal << 8 | nBitsTotal >>> 24) & 0x00ff00ff | (nBitsTotal << 24 | nBitsTotal >>> 8) & 0xff00ff00;
        data.sigBytes = (dataWords.length + 1) * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var hash = this._hash;
        var H = hash.words;

        // Swap endian
        for (var i = 0; i < 5; i++) {
          // Shortcut
          var H_i = H[i];

          // Swap
          H[i] = (H_i << 8 | H_i >>> 24) & 0x00ff00ff | (H_i << 24 | H_i >>> 8) & 0xff00ff00;
        }

        // Return final computed hash
        return hash;
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });
    function f1(x, y, z) {
      return x ^ y ^ z;
    }
    function f2(x, y, z) {
      return x & y | ~x & z;
    }
    function f3(x, y, z) {
      return (x | ~y) ^ z;
    }
    function f4(x, y, z) {
      return x & z | y & ~z;
    }
    function f5(x, y, z) {
      return x ^ (y | ~z);
    }
    function rotl(x, n) {
      return x << n | x >>> 32 - n;
    }

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.RIPEMD160('message');
     *     var hash = CryptoJS.RIPEMD160(wordArray);
     */
    C.RIPEMD160 = Hasher._createHelper(RIPEMD160);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacRIPEMD160(message, key);
     */
    C.HmacRIPEMD160 = Hasher._createHmacHelper(RIPEMD160);
  })(Math);
  return CryptoJS.RIPEMD160;
});

/***/ }),

/***/ 37146:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Reusable object
    var W = [];

    /**
     * SHA-1 hash algorithm.
     */
    var SHA1 = C_algo.SHA1 = Hasher.extend({
      _doReset: function _doReset() {
        this._hash = new WordArray.init([0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476, 0xc3d2e1f0]);
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcut
        var H = this._hash.words;

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];
        var e = H[4];

        // Computation
        for (var i = 0; i < 80; i++) {
          if (i < 16) {
            W[i] = M[offset + i] | 0;
          } else {
            var n = W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16];
            W[i] = n << 1 | n >>> 31;
          }
          var t = (a << 5 | a >>> 27) + e + W[i];
          if (i < 20) {
            t += (b & c | ~b & d) + 0x5a827999;
          } else if (i < 40) {
            t += (b ^ c ^ d) + 0x6ed9eba1;
          } else if (i < 60) {
            t += (b & c | b & d | c & d) - 0x70e44324;
          } else /* if (i < 80) */{
              t += (b ^ c ^ d) - 0x359d3e2a;
            }
          e = d;
          d = c;
          c = b << 30 | b >>> 2;
          b = a;
          a = t;
        }

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
        H[4] = H[4] + e | 0;
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Return final computed hash
        return this._hash;
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA1('message');
     *     var hash = CryptoJS.SHA1(wordArray);
     */
    C.SHA1 = Hasher._createHelper(SHA1);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA1(message, key);
     */
    C.HmacSHA1 = Hasher._createHmacHelper(SHA1);
  })();
  return CryptoJS.SHA1;
});

/***/ }),

/***/ 11905:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66664));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var C_algo = C.algo;
    var SHA256 = C_algo.SHA256;

    /**
     * SHA-224 hash algorithm.
     */
    var SHA224 = C_algo.SHA224 = SHA256.extend({
      _doReset: function _doReset() {
        this._hash = new WordArray.init([0xc1059ed8, 0x367cd507, 0x3070dd17, 0xf70e5939, 0xffc00b31, 0x68581511, 0x64f98fa7, 0xbefa4fa4]);
      },
      _doFinalize: function _doFinalize() {
        var hash = SHA256._doFinalize.call(this);
        hash.sigBytes -= 4;
        return hash;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA224('message');
     *     var hash = CryptoJS.SHA224(wordArray);
     */
    C.SHA224 = SHA256._createHelper(SHA224);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA224(message, key);
     */
    C.HmacSHA224 = SHA256._createHmacHelper(SHA224);
  })();
  return CryptoJS.SHA224;
});

/***/ }),

/***/ 66664:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_algo = C.algo;

    // Initialization and round constants tables
    var H = [];
    var K = [];

    // Compute constants
    (function () {
      function isPrime(n) {
        var sqrtN = Math.sqrt(n);
        for (var factor = 2; factor <= sqrtN; factor++) {
          if (!(n % factor)) {
            return false;
          }
        }
        return true;
      }
      function getFractionalBits(n) {
        return (n - (n | 0)) * 0x100000000 | 0;
      }
      var n = 2;
      var nPrime = 0;
      while (nPrime < 64) {
        if (isPrime(n)) {
          if (nPrime < 8) {
            H[nPrime] = getFractionalBits(Math.pow(n, 1 / 2));
          }
          K[nPrime] = getFractionalBits(Math.pow(n, 1 / 3));
          nPrime++;
        }
        n++;
      }
    })();

    // Reusable object
    var W = [];

    /**
     * SHA-256 hash algorithm.
     */
    var SHA256 = C_algo.SHA256 = Hasher.extend({
      _doReset: function _doReset() {
        this._hash = new WordArray.init(H.slice(0));
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcut
        var H = this._hash.words;

        // Working variables
        var a = H[0];
        var b = H[1];
        var c = H[2];
        var d = H[3];
        var e = H[4];
        var f = H[5];
        var g = H[6];
        var h = H[7];

        // Computation
        for (var i = 0; i < 64; i++) {
          if (i < 16) {
            W[i] = M[offset + i] | 0;
          } else {
            var gamma0x = W[i - 15];
            var gamma0 = (gamma0x << 25 | gamma0x >>> 7) ^ (gamma0x << 14 | gamma0x >>> 18) ^ gamma0x >>> 3;
            var gamma1x = W[i - 2];
            var gamma1 = (gamma1x << 15 | gamma1x >>> 17) ^ (gamma1x << 13 | gamma1x >>> 19) ^ gamma1x >>> 10;
            W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16];
          }
          var ch = e & f ^ ~e & g;
          var maj = a & b ^ a & c ^ b & c;
          var sigma0 = (a << 30 | a >>> 2) ^ (a << 19 | a >>> 13) ^ (a << 10 | a >>> 22);
          var sigma1 = (e << 26 | e >>> 6) ^ (e << 21 | e >>> 11) ^ (e << 7 | e >>> 25);
          var t1 = h + sigma1 + ch + K[i] + W[i];
          var t2 = sigma0 + maj;
          h = g;
          g = f;
          f = e;
          e = d + t1 | 0;
          d = c;
          c = b;
          b = a;
          a = t1 + t2 | 0;
        }

        // Intermediate hash value
        H[0] = H[0] + a | 0;
        H[1] = H[1] + b | 0;
        H[2] = H[2] + c | 0;
        H[3] = H[3] + d | 0;
        H[4] = H[4] + e | 0;
        H[5] = H[5] + f | 0;
        H[6] = H[6] + g | 0;
        H[7] = H[7] + h | 0;
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Return final computed hash
        return this._hash;
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA256('message');
     *     var hash = CryptoJS.SHA256(wordArray);
     */
    C.SHA256 = Hasher._createHelper(SHA256);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA256(message, key);
     */
    C.HmacSHA256 = Hasher._createHmacHelper(SHA256);
  })(Math);
  return CryptoJS.SHA256;
});

/***/ }),

/***/ 16720:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(18909));
  } else {}
})(void 0, function (CryptoJS) {
  (function (Math) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var Hasher = C_lib.Hasher;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var C_algo = C.algo;

    // Constants tables
    var RHO_OFFSETS = [];
    var PI_INDEXES = [];
    var ROUND_CONSTANTS = [];

    // Compute Constants
    (function () {
      // Compute rho offset constants
      var x = 1,
        y = 0;
      for (var t = 0; t < 24; t++) {
        RHO_OFFSETS[x + 5 * y] = (t + 1) * (t + 2) / 2 % 64;
        var newX = y % 5;
        var newY = (2 * x + 3 * y) % 5;
        x = newX;
        y = newY;
      }

      // Compute pi index constants
      for (var x = 0; x < 5; x++) {
        for (var y = 0; y < 5; y++) {
          PI_INDEXES[x + 5 * y] = y + (2 * x + 3 * y) % 5 * 5;
        }
      }

      // Compute round constants
      var LFSR = 0x01;
      for (var i = 0; i < 24; i++) {
        var roundConstantMsw = 0;
        var roundConstantLsw = 0;
        for (var j = 0; j < 7; j++) {
          if (LFSR & 0x01) {
            var bitPosition = (1 << j) - 1;
            if (bitPosition < 32) {
              roundConstantLsw ^= 1 << bitPosition;
            } else /* if (bitPosition >= 32) */{
                roundConstantMsw ^= 1 << bitPosition - 32;
              }
          }

          // Compute next LFSR
          if (LFSR & 0x80) {
            // Primitive polynomial over GF(2): x^8 + x^6 + x^5 + x^4 + 1
            LFSR = LFSR << 1 ^ 0x71;
          } else {
            LFSR <<= 1;
          }
        }
        ROUND_CONSTANTS[i] = X64Word.create(roundConstantMsw, roundConstantLsw);
      }
    })();

    // Reusable objects for temporary values
    var T = [];
    (function () {
      for (var i = 0; i < 25; i++) {
        T[i] = X64Word.create();
      }
    })();

    /**
     * SHA-3 hash algorithm.
     */
    var SHA3 = C_algo.SHA3 = Hasher.extend({
      /**
       * Configuration options.
       *
       * @property {number} outputLength
       *   The desired number of bits in the output hash.
       *   Only values permitted are: 224, 256, 384, 512.
       *   Default: 512
       */
      cfg: Hasher.cfg.extend({
        outputLength: 512
      }),
      _doReset: function _doReset() {
        var state = this._state = [];
        for (var i = 0; i < 25; i++) {
          state[i] = new X64Word.init();
        }
        this.blockSize = (1600 - 2 * this.cfg.outputLength) / 32;
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcuts
        var state = this._state;
        var nBlockSizeLanes = this.blockSize / 2;

        // Absorb
        for (var i = 0; i < nBlockSizeLanes; i++) {
          // Shortcuts
          var M2i = M[offset + 2 * i];
          var M2i1 = M[offset + 2 * i + 1];

          // Swap endian
          M2i = (M2i << 8 | M2i >>> 24) & 0x00ff00ff | (M2i << 24 | M2i >>> 8) & 0xff00ff00;
          M2i1 = (M2i1 << 8 | M2i1 >>> 24) & 0x00ff00ff | (M2i1 << 24 | M2i1 >>> 8) & 0xff00ff00;

          // Absorb message into state
          var lane = state[i];
          lane.high ^= M2i1;
          lane.low ^= M2i;
        }

        // Rounds
        for (var round = 0; round < 24; round++) {
          // Theta
          for (var x = 0; x < 5; x++) {
            // Mix column lanes
            var tMsw = 0,
              tLsw = 0;
            for (var y = 0; y < 5; y++) {
              var lane = state[x + 5 * y];
              tMsw ^= lane.high;
              tLsw ^= lane.low;
            }

            // Temporary values
            var Tx = T[x];
            Tx.high = tMsw;
            Tx.low = tLsw;
          }
          for (var x = 0; x < 5; x++) {
            // Shortcuts
            var Tx4 = T[(x + 4) % 5];
            var Tx1 = T[(x + 1) % 5];
            var Tx1Msw = Tx1.high;
            var Tx1Lsw = Tx1.low;

            // Mix surrounding columns
            var tMsw = Tx4.high ^ (Tx1Msw << 1 | Tx1Lsw >>> 31);
            var tLsw = Tx4.low ^ (Tx1Lsw << 1 | Tx1Msw >>> 31);
            for (var y = 0; y < 5; y++) {
              var lane = state[x + 5 * y];
              lane.high ^= tMsw;
              lane.low ^= tLsw;
            }
          }

          // Rho Pi
          for (var laneIndex = 1; laneIndex < 25; laneIndex++) {
            var tMsw;
            var tLsw;

            // Shortcuts
            var lane = state[laneIndex];
            var laneMsw = lane.high;
            var laneLsw = lane.low;
            var rhoOffset = RHO_OFFSETS[laneIndex];

            // Rotate lanes
            if (rhoOffset < 32) {
              tMsw = laneMsw << rhoOffset | laneLsw >>> 32 - rhoOffset;
              tLsw = laneLsw << rhoOffset | laneMsw >>> 32 - rhoOffset;
            } else /* if (rhoOffset >= 32) */{
                tMsw = laneLsw << rhoOffset - 32 | laneMsw >>> 64 - rhoOffset;
                tLsw = laneMsw << rhoOffset - 32 | laneLsw >>> 64 - rhoOffset;
              }

            // Transpose lanes
            var TPiLane = T[PI_INDEXES[laneIndex]];
            TPiLane.high = tMsw;
            TPiLane.low = tLsw;
          }

          // Rho pi at x = y = 0
          var T0 = T[0];
          var state0 = state[0];
          T0.high = state0.high;
          T0.low = state0.low;

          // Chi
          for (var x = 0; x < 5; x++) {
            for (var y = 0; y < 5; y++) {
              // Shortcuts
              var laneIndex = x + 5 * y;
              var lane = state[laneIndex];
              var TLane = T[laneIndex];
              var Tx1Lane = T[(x + 1) % 5 + 5 * y];
              var Tx2Lane = T[(x + 2) % 5 + 5 * y];

              // Mix rows
              lane.high = TLane.high ^ ~Tx1Lane.high & Tx2Lane.high;
              lane.low = TLane.low ^ ~Tx1Lane.low & Tx2Lane.low;
            }
          }

          // Iota
          var lane = state[0];
          var roundConstant = ROUND_CONSTANTS[round];
          lane.high ^= roundConstant.high;
          lane.low ^= roundConstant.low;
        }
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;
        var blockSizeBits = this.blockSize * 32;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x1 << 24 - nBitsLeft % 32;
        dataWords[(Math.ceil((nBitsLeft + 1) / blockSizeBits) * blockSizeBits >>> 5) - 1] |= 0x80;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Shortcuts
        var state = this._state;
        var outputLengthBytes = this.cfg.outputLength / 8;
        var outputLengthLanes = outputLengthBytes / 8;

        // Squeeze
        var hashWords = [];
        for (var i = 0; i < outputLengthLanes; i++) {
          // Shortcuts
          var lane = state[i];
          var laneMsw = lane.high;
          var laneLsw = lane.low;

          // Swap endian
          laneMsw = (laneMsw << 8 | laneMsw >>> 24) & 0x00ff00ff | (laneMsw << 24 | laneMsw >>> 8) & 0xff00ff00;
          laneLsw = (laneLsw << 8 | laneLsw >>> 24) & 0x00ff00ff | (laneLsw << 24 | laneLsw >>> 8) & 0xff00ff00;

          // Squeeze state to retrieve hash
          hashWords.push(laneLsw);
          hashWords.push(laneMsw);
        }

        // Return final computed hash
        return new WordArray.init(hashWords, outputLengthBytes);
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        var state = clone._state = this._state.slice(0);
        for (var i = 0; i < 25; i++) {
          state[i] = state[i].clone();
        }
        return clone;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA3('message');
     *     var hash = CryptoJS.SHA3(wordArray);
     */
    C.SHA3 = Hasher._createHelper(SHA3);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA3(message, key);
     */
    C.HmacSHA3 = Hasher._createHmacHelper(SHA3);
  })(Math);
  return CryptoJS.SHA3;
});

/***/ }),

/***/ 27772:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(18909), __webpack_require__(51965));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var X64WordArray = C_x64.WordArray;
    var C_algo = C.algo;
    var SHA512 = C_algo.SHA512;

    /**
     * SHA-384 hash algorithm.
     */
    var SHA384 = C_algo.SHA384 = SHA512.extend({
      _doReset: function _doReset() {
        this._hash = new X64WordArray.init([new X64Word.init(0xcbbb9d5d, 0xc1059ed8), new X64Word.init(0x629a292a, 0x367cd507), new X64Word.init(0x9159015a, 0x3070dd17), new X64Word.init(0x152fecd8, 0xf70e5939), new X64Word.init(0x67332667, 0xffc00b31), new X64Word.init(0x8eb44a87, 0x68581511), new X64Word.init(0xdb0c2e0d, 0x64f98fa7), new X64Word.init(0x47b5481d, 0xbefa4fa4)]);
      },
      _doFinalize: function _doFinalize() {
        var hash = SHA512._doFinalize.call(this);
        hash.sigBytes -= 16;
        return hash;
      }
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA384('message');
     *     var hash = CryptoJS.SHA384(wordArray);
     */
    C.SHA384 = SHA512._createHelper(SHA384);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA384(message, key);
     */
    C.HmacSHA384 = SHA512._createHmacHelper(SHA384);
  })();
  return CryptoJS.SHA384;
});

/***/ }),

/***/ 51965:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(18909));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Hasher = C_lib.Hasher;
    var C_x64 = C.x64;
    var X64Word = C_x64.Word;
    var X64WordArray = C_x64.WordArray;
    var C_algo = C.algo;
    function X64Word_create() {
      return X64Word.create.apply(X64Word, arguments);
    }

    // Constants
    var K = [X64Word_create(0x428a2f98, 0xd728ae22), X64Word_create(0x71374491, 0x23ef65cd), X64Word_create(0xb5c0fbcf, 0xec4d3b2f), X64Word_create(0xe9b5dba5, 0x8189dbbc), X64Word_create(0x3956c25b, 0xf348b538), X64Word_create(0x59f111f1, 0xb605d019), X64Word_create(0x923f82a4, 0xaf194f9b), X64Word_create(0xab1c5ed5, 0xda6d8118), X64Word_create(0xd807aa98, 0xa3030242), X64Word_create(0x12835b01, 0x45706fbe), X64Word_create(0x243185be, 0x4ee4b28c), X64Word_create(0x550c7dc3, 0xd5ffb4e2), X64Word_create(0x72be5d74, 0xf27b896f), X64Word_create(0x80deb1fe, 0x3b1696b1), X64Word_create(0x9bdc06a7, 0x25c71235), X64Word_create(0xc19bf174, 0xcf692694), X64Word_create(0xe49b69c1, 0x9ef14ad2), X64Word_create(0xefbe4786, 0x384f25e3), X64Word_create(0x0fc19dc6, 0x8b8cd5b5), X64Word_create(0x240ca1cc, 0x77ac9c65), X64Word_create(0x2de92c6f, 0x592b0275), X64Word_create(0x4a7484aa, 0x6ea6e483), X64Word_create(0x5cb0a9dc, 0xbd41fbd4), X64Word_create(0x76f988da, 0x831153b5), X64Word_create(0x983e5152, 0xee66dfab), X64Word_create(0xa831c66d, 0x2db43210), X64Word_create(0xb00327c8, 0x98fb213f), X64Word_create(0xbf597fc7, 0xbeef0ee4), X64Word_create(0xc6e00bf3, 0x3da88fc2), X64Word_create(0xd5a79147, 0x930aa725), X64Word_create(0x06ca6351, 0xe003826f), X64Word_create(0x14292967, 0x0a0e6e70), X64Word_create(0x27b70a85, 0x46d22ffc), X64Word_create(0x2e1b2138, 0x5c26c926), X64Word_create(0x4d2c6dfc, 0x5ac42aed), X64Word_create(0x53380d13, 0x9d95b3df), X64Word_create(0x650a7354, 0x8baf63de), X64Word_create(0x766a0abb, 0x3c77b2a8), X64Word_create(0x81c2c92e, 0x47edaee6), X64Word_create(0x92722c85, 0x1482353b), X64Word_create(0xa2bfe8a1, 0x4cf10364), X64Word_create(0xa81a664b, 0xbc423001), X64Word_create(0xc24b8b70, 0xd0f89791), X64Word_create(0xc76c51a3, 0x0654be30), X64Word_create(0xd192e819, 0xd6ef5218), X64Word_create(0xd6990624, 0x5565a910), X64Word_create(0xf40e3585, 0x5771202a), X64Word_create(0x106aa070, 0x32bbd1b8), X64Word_create(0x19a4c116, 0xb8d2d0c8), X64Word_create(0x1e376c08, 0x5141ab53), X64Word_create(0x2748774c, 0xdf8eeb99), X64Word_create(0x34b0bcb5, 0xe19b48a8), X64Word_create(0x391c0cb3, 0xc5c95a63), X64Word_create(0x4ed8aa4a, 0xe3418acb), X64Word_create(0x5b9cca4f, 0x7763e373), X64Word_create(0x682e6ff3, 0xd6b2b8a3), X64Word_create(0x748f82ee, 0x5defb2fc), X64Word_create(0x78a5636f, 0x43172f60), X64Word_create(0x84c87814, 0xa1f0ab72), X64Word_create(0x8cc70208, 0x1a6439ec), X64Word_create(0x90befffa, 0x23631e28), X64Word_create(0xa4506ceb, 0xde82bde9), X64Word_create(0xbef9a3f7, 0xb2c67915), X64Word_create(0xc67178f2, 0xe372532b), X64Word_create(0xca273ece, 0xea26619c), X64Word_create(0xd186b8c7, 0x21c0c207), X64Word_create(0xeada7dd6, 0xcde0eb1e), X64Word_create(0xf57d4f7f, 0xee6ed178), X64Word_create(0x06f067aa, 0x72176fba), X64Word_create(0x0a637dc5, 0xa2c898a6), X64Word_create(0x113f9804, 0xbef90dae), X64Word_create(0x1b710b35, 0x131c471b), X64Word_create(0x28db77f5, 0x23047d84), X64Word_create(0x32caab7b, 0x40c72493), X64Word_create(0x3c9ebe0a, 0x15c9bebc), X64Word_create(0x431d67c4, 0x9c100d4c), X64Word_create(0x4cc5d4be, 0xcb3e42b6), X64Word_create(0x597f299c, 0xfc657e2a), X64Word_create(0x5fcb6fab, 0x3ad6faec), X64Word_create(0x6c44198c, 0x4a475817)];

    // Reusable objects
    var W = [];
    (function () {
      for (var i = 0; i < 80; i++) {
        W[i] = X64Word_create();
      }
    })();

    /**
     * SHA-512 hash algorithm.
     */
    var SHA512 = C_algo.SHA512 = Hasher.extend({
      _doReset: function _doReset() {
        this._hash = new X64WordArray.init([new X64Word.init(0x6a09e667, 0xf3bcc908), new X64Word.init(0xbb67ae85, 0x84caa73b), new X64Word.init(0x3c6ef372, 0xfe94f82b), new X64Word.init(0xa54ff53a, 0x5f1d36f1), new X64Word.init(0x510e527f, 0xade682d1), new X64Word.init(0x9b05688c, 0x2b3e6c1f), new X64Word.init(0x1f83d9ab, 0xfb41bd6b), new X64Word.init(0x5be0cd19, 0x137e2179)]);
      },
      _doProcessBlock: function _doProcessBlock(M, offset) {
        // Shortcuts
        var H = this._hash.words;
        var H0 = H[0];
        var H1 = H[1];
        var H2 = H[2];
        var H3 = H[3];
        var H4 = H[4];
        var H5 = H[5];
        var H6 = H[6];
        var H7 = H[7];
        var H0h = H0.high;
        var H0l = H0.low;
        var H1h = H1.high;
        var H1l = H1.low;
        var H2h = H2.high;
        var H2l = H2.low;
        var H3h = H3.high;
        var H3l = H3.low;
        var H4h = H4.high;
        var H4l = H4.low;
        var H5h = H5.high;
        var H5l = H5.low;
        var H6h = H6.high;
        var H6l = H6.low;
        var H7h = H7.high;
        var H7l = H7.low;

        // Working variables
        var ah = H0h;
        var al = H0l;
        var bh = H1h;
        var bl = H1l;
        var ch = H2h;
        var cl = H2l;
        var dh = H3h;
        var dl = H3l;
        var eh = H4h;
        var el = H4l;
        var fh = H5h;
        var fl = H5l;
        var gh = H6h;
        var gl = H6l;
        var hh = H7h;
        var hl = H7l;

        // Rounds
        for (var i = 0; i < 80; i++) {
          var Wil;
          var Wih;

          // Shortcut
          var Wi = W[i];

          // Extend message
          if (i < 16) {
            Wih = Wi.high = M[offset + i * 2] | 0;
            Wil = Wi.low = M[offset + i * 2 + 1] | 0;
          } else {
            // Gamma0
            var gamma0x = W[i - 15];
            var gamma0xh = gamma0x.high;
            var gamma0xl = gamma0x.low;
            var gamma0h = (gamma0xh >>> 1 | gamma0xl << 31) ^ (gamma0xh >>> 8 | gamma0xl << 24) ^ gamma0xh >>> 7;
            var gamma0l = (gamma0xl >>> 1 | gamma0xh << 31) ^ (gamma0xl >>> 8 | gamma0xh << 24) ^ (gamma0xl >>> 7 | gamma0xh << 25);

            // Gamma1
            var gamma1x = W[i - 2];
            var gamma1xh = gamma1x.high;
            var gamma1xl = gamma1x.low;
            var gamma1h = (gamma1xh >>> 19 | gamma1xl << 13) ^ (gamma1xh << 3 | gamma1xl >>> 29) ^ gamma1xh >>> 6;
            var gamma1l = (gamma1xl >>> 19 | gamma1xh << 13) ^ (gamma1xl << 3 | gamma1xh >>> 29) ^ (gamma1xl >>> 6 | gamma1xh << 26);

            // W[i] = gamma0 + W[i - 7] + gamma1 + W[i - 16]
            var Wi7 = W[i - 7];
            var Wi7h = Wi7.high;
            var Wi7l = Wi7.low;
            var Wi16 = W[i - 16];
            var Wi16h = Wi16.high;
            var Wi16l = Wi16.low;
            Wil = gamma0l + Wi7l;
            Wih = gamma0h + Wi7h + (Wil >>> 0 < gamma0l >>> 0 ? 1 : 0);
            Wil = Wil + gamma1l;
            Wih = Wih + gamma1h + (Wil >>> 0 < gamma1l >>> 0 ? 1 : 0);
            Wil = Wil + Wi16l;
            Wih = Wih + Wi16h + (Wil >>> 0 < Wi16l >>> 0 ? 1 : 0);
            Wi.high = Wih;
            Wi.low = Wil;
          }
          var chh = eh & fh ^ ~eh & gh;
          var chl = el & fl ^ ~el & gl;
          var majh = ah & bh ^ ah & ch ^ bh & ch;
          var majl = al & bl ^ al & cl ^ bl & cl;
          var sigma0h = (ah >>> 28 | al << 4) ^ (ah << 30 | al >>> 2) ^ (ah << 25 | al >>> 7);
          var sigma0l = (al >>> 28 | ah << 4) ^ (al << 30 | ah >>> 2) ^ (al << 25 | ah >>> 7);
          var sigma1h = (eh >>> 14 | el << 18) ^ (eh >>> 18 | el << 14) ^ (eh << 23 | el >>> 9);
          var sigma1l = (el >>> 14 | eh << 18) ^ (el >>> 18 | eh << 14) ^ (el << 23 | eh >>> 9);

          // t1 = h + sigma1 + ch + K[i] + W[i]
          var Ki = K[i];
          var Kih = Ki.high;
          var Kil = Ki.low;
          var t1l = hl + sigma1l;
          var t1h = hh + sigma1h + (t1l >>> 0 < hl >>> 0 ? 1 : 0);
          var t1l = t1l + chl;
          var t1h = t1h + chh + (t1l >>> 0 < chl >>> 0 ? 1 : 0);
          var t1l = t1l + Kil;
          var t1h = t1h + Kih + (t1l >>> 0 < Kil >>> 0 ? 1 : 0);
          var t1l = t1l + Wil;
          var t1h = t1h + Wih + (t1l >>> 0 < Wil >>> 0 ? 1 : 0);

          // t2 = sigma0 + maj
          var t2l = sigma0l + majl;
          var t2h = sigma0h + majh + (t2l >>> 0 < sigma0l >>> 0 ? 1 : 0);

          // Update working variables
          hh = gh;
          hl = gl;
          gh = fh;
          gl = fl;
          fh = eh;
          fl = el;
          el = dl + t1l | 0;
          eh = dh + t1h + (el >>> 0 < dl >>> 0 ? 1 : 0) | 0;
          dh = ch;
          dl = cl;
          ch = bh;
          cl = bl;
          bh = ah;
          bl = al;
          al = t1l + t2l | 0;
          ah = t1h + t2h + (al >>> 0 < t1l >>> 0 ? 1 : 0) | 0;
        }

        // Intermediate hash value
        H0l = H0.low = H0l + al;
        H0.high = H0h + ah + (H0l >>> 0 < al >>> 0 ? 1 : 0);
        H1l = H1.low = H1l + bl;
        H1.high = H1h + bh + (H1l >>> 0 < bl >>> 0 ? 1 : 0);
        H2l = H2.low = H2l + cl;
        H2.high = H2h + ch + (H2l >>> 0 < cl >>> 0 ? 1 : 0);
        H3l = H3.low = H3l + dl;
        H3.high = H3h + dh + (H3l >>> 0 < dl >>> 0 ? 1 : 0);
        H4l = H4.low = H4l + el;
        H4.high = H4h + eh + (H4l >>> 0 < el >>> 0 ? 1 : 0);
        H5l = H5.low = H5l + fl;
        H5.high = H5h + fh + (H5l >>> 0 < fl >>> 0 ? 1 : 0);
        H6l = H6.low = H6l + gl;
        H6.high = H6h + gh + (H6l >>> 0 < gl >>> 0 ? 1 : 0);
        H7l = H7.low = H7l + hl;
        H7.high = H7h + hh + (H7l >>> 0 < hl >>> 0 ? 1 : 0);
      },
      _doFinalize: function _doFinalize() {
        // Shortcuts
        var data = this._data;
        var dataWords = data.words;
        var nBitsTotal = this._nDataBytes * 8;
        var nBitsLeft = data.sigBytes * 8;

        // Add padding
        dataWords[nBitsLeft >>> 5] |= 0x80 << 24 - nBitsLeft % 32;
        dataWords[(nBitsLeft + 128 >>> 10 << 5) + 30] = Math.floor(nBitsTotal / 0x100000000);
        dataWords[(nBitsLeft + 128 >>> 10 << 5) + 31] = nBitsTotal;
        data.sigBytes = dataWords.length * 4;

        // Hash final blocks
        this._process();

        // Convert hash to 32-bit word array before returning
        var hash = this._hash.toX32();

        // Return final computed hash
        return hash;
      },
      clone: function clone() {
        var clone = Hasher.clone.call(this);
        clone._hash = this._hash.clone();
        return clone;
      },
      blockSize: 1024 / 32
    });

    /**
     * Shortcut function to the hasher's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     *
     * @return {WordArray} The hash.
     *
     * @static
     *
     * @example
     *
     *     var hash = CryptoJS.SHA512('message');
     *     var hash = CryptoJS.SHA512(wordArray);
     */
    C.SHA512 = Hasher._createHelper(SHA512);

    /**
     * Shortcut function to the HMAC's object interface.
     *
     * @param {WordArray|string} message The message to hash.
     * @param {WordArray|string} key The secret key.
     *
     * @return {WordArray} The HMAC.
     *
     * @static
     *
     * @example
     *
     *     var hmac = CryptoJS.HmacSHA512(message, key);
     */
    C.HmacSHA512 = Hasher._createHmacHelper(SHA512);
  })();
  return CryptoJS.SHA512;
});

/***/ }),

/***/ 24107:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory, undef) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352), __webpack_require__(66947), __webpack_require__(68319), __webpack_require__(82747), __webpack_require__(51270));
  } else {}
})(void 0, function (CryptoJS) {
  (function () {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var WordArray = C_lib.WordArray;
    var BlockCipher = C_lib.BlockCipher;
    var C_algo = C.algo;

    // Permuted Choice 1 constants
    var PC1 = [57, 49, 41, 33, 25, 17, 9, 1, 58, 50, 42, 34, 26, 18, 10, 2, 59, 51, 43, 35, 27, 19, 11, 3, 60, 52, 44, 36, 63, 55, 47, 39, 31, 23, 15, 7, 62, 54, 46, 38, 30, 22, 14, 6, 61, 53, 45, 37, 29, 21, 13, 5, 28, 20, 12, 4];

    // Permuted Choice 2 constants
    var PC2 = [14, 17, 11, 24, 1, 5, 3, 28, 15, 6, 21, 10, 23, 19, 12, 4, 26, 8, 16, 7, 27, 20, 13, 2, 41, 52, 31, 37, 47, 55, 30, 40, 51, 45, 33, 48, 44, 49, 39, 56, 34, 53, 46, 42, 50, 36, 29, 32];

    // Cumulative bit shift constants
    var BIT_SHIFTS = [1, 2, 4, 6, 8, 10, 12, 14, 15, 17, 19, 21, 23, 25, 27, 28];

    // SBOXes and round permutation constants
    var SBOX_P = [{
      0x0: 0x808200,
      0x10000000: 0x8000,
      0x20000000: 0x808002,
      0x30000000: 0x2,
      0x40000000: 0x200,
      0x50000000: 0x808202,
      0x60000000: 0x800202,
      0x70000000: 0x800000,
      0x80000000: 0x202,
      0x90000000: 0x800200,
      0xa0000000: 0x8200,
      0xb0000000: 0x808000,
      0xc0000000: 0x8002,
      0xd0000000: 0x800002,
      0xe0000000: 0x0,
      0xf0000000: 0x8202,
      0x8000000: 0x0,
      0x18000000: 0x808202,
      0x28000000: 0x8202,
      0x38000000: 0x8000,
      0x48000000: 0x808200,
      0x58000000: 0x200,
      0x68000000: 0x808002,
      0x78000000: 0x2,
      0x88000000: 0x800200,
      0x98000000: 0x8200,
      0xa8000000: 0x808000,
      0xb8000000: 0x800202,
      0xc8000000: 0x800002,
      0xd8000000: 0x8002,
      0xe8000000: 0x202,
      0xf8000000: 0x800000,
      0x1: 0x8000,
      0x10000001: 0x2,
      0x20000001: 0x808200,
      0x30000001: 0x800000,
      0x40000001: 0x808002,
      0x50000001: 0x8200,
      0x60000001: 0x200,
      0x70000001: 0x800202,
      0x80000001: 0x808202,
      0x90000001: 0x808000,
      0xa0000001: 0x800002,
      0xb0000001: 0x8202,
      0xc0000001: 0x202,
      0xd0000001: 0x800200,
      0xe0000001: 0x8002,
      0xf0000001: 0x0,
      0x8000001: 0x808202,
      0x18000001: 0x808000,
      0x28000001: 0x800000,
      0x38000001: 0x200,
      0x48000001: 0x8000,
      0x58000001: 0x800002,
      0x68000001: 0x2,
      0x78000001: 0x8202,
      0x88000001: 0x8002,
      0x98000001: 0x800202,
      0xa8000001: 0x202,
      0xb8000001: 0x808200,
      0xc8000001: 0x800200,
      0xd8000001: 0x0,
      0xe8000001: 0x8200,
      0xf8000001: 0x808002
    }, {
      0x0: 0x40084010,
      0x1000000: 0x4000,
      0x2000000: 0x80000,
      0x3000000: 0x40080010,
      0x4000000: 0x40000010,
      0x5000000: 0x40084000,
      0x6000000: 0x40004000,
      0x7000000: 0x10,
      0x8000000: 0x84000,
      0x9000000: 0x40004010,
      0xa000000: 0x40000000,
      0xb000000: 0x84010,
      0xc000000: 0x80010,
      0xd000000: 0x0,
      0xe000000: 0x4010,
      0xf000000: 0x40080000,
      0x800000: 0x40004000,
      0x1800000: 0x84010,
      0x2800000: 0x10,
      0x3800000: 0x40004010,
      0x4800000: 0x40084010,
      0x5800000: 0x40000000,
      0x6800000: 0x80000,
      0x7800000: 0x40080010,
      0x8800000: 0x80010,
      0x9800000: 0x0,
      0xa800000: 0x4000,
      0xb800000: 0x40080000,
      0xc800000: 0x40000010,
      0xd800000: 0x84000,
      0xe800000: 0x40084000,
      0xf800000: 0x4010,
      0x10000000: 0x0,
      0x11000000: 0x40080010,
      0x12000000: 0x40004010,
      0x13000000: 0x40084000,
      0x14000000: 0x40080000,
      0x15000000: 0x10,
      0x16000000: 0x84010,
      0x17000000: 0x4000,
      0x18000000: 0x4010,
      0x19000000: 0x80000,
      0x1a000000: 0x80010,
      0x1b000000: 0x40000010,
      0x1c000000: 0x84000,
      0x1d000000: 0x40004000,
      0x1e000000: 0x40000000,
      0x1f000000: 0x40084010,
      0x10800000: 0x84010,
      0x11800000: 0x80000,
      0x12800000: 0x40080000,
      0x13800000: 0x4000,
      0x14800000: 0x40004000,
      0x15800000: 0x40084010,
      0x16800000: 0x10,
      0x17800000: 0x40000000,
      0x18800000: 0x40084000,
      0x19800000: 0x40000010,
      0x1a800000: 0x40004010,
      0x1b800000: 0x80010,
      0x1c800000: 0x0,
      0x1d800000: 0x4010,
      0x1e800000: 0x40080010,
      0x1f800000: 0x84000
    }, {
      0x0: 0x104,
      0x100000: 0x0,
      0x200000: 0x4000100,
      0x300000: 0x10104,
      0x400000: 0x10004,
      0x500000: 0x4000004,
      0x600000: 0x4010104,
      0x700000: 0x4010000,
      0x800000: 0x4000000,
      0x900000: 0x4010100,
      0xa00000: 0x10100,
      0xb00000: 0x4010004,
      0xc00000: 0x4000104,
      0xd00000: 0x10000,
      0xe00000: 0x4,
      0xf00000: 0x100,
      0x80000: 0x4010100,
      0x180000: 0x4010004,
      0x280000: 0x0,
      0x380000: 0x4000100,
      0x480000: 0x4000004,
      0x580000: 0x10000,
      0x680000: 0x10004,
      0x780000: 0x104,
      0x880000: 0x4,
      0x980000: 0x100,
      0xa80000: 0x4010000,
      0xb80000: 0x10104,
      0xc80000: 0x10100,
      0xd80000: 0x4000104,
      0xe80000: 0x4010104,
      0xf80000: 0x4000000,
      0x1000000: 0x4010100,
      0x1100000: 0x10004,
      0x1200000: 0x10000,
      0x1300000: 0x4000100,
      0x1400000: 0x100,
      0x1500000: 0x4010104,
      0x1600000: 0x4000004,
      0x1700000: 0x0,
      0x1800000: 0x4000104,
      0x1900000: 0x4000000,
      0x1a00000: 0x4,
      0x1b00000: 0x10100,
      0x1c00000: 0x4010000,
      0x1d00000: 0x104,
      0x1e00000: 0x10104,
      0x1f00000: 0x4010004,
      0x1080000: 0x4000000,
      0x1180000: 0x104,
      0x1280000: 0x4010100,
      0x1380000: 0x0,
      0x1480000: 0x10004,
      0x1580000: 0x4000100,
      0x1680000: 0x100,
      0x1780000: 0x4010004,
      0x1880000: 0x10000,
      0x1980000: 0x4010104,
      0x1a80000: 0x10104,
      0x1b80000: 0x4000004,
      0x1c80000: 0x4000104,
      0x1d80000: 0x4010000,
      0x1e80000: 0x4,
      0x1f80000: 0x10100
    }, {
      0x0: 0x80401000,
      0x10000: 0x80001040,
      0x20000: 0x401040,
      0x30000: 0x80400000,
      0x40000: 0x0,
      0x50000: 0x401000,
      0x60000: 0x80000040,
      0x70000: 0x400040,
      0x80000: 0x80000000,
      0x90000: 0x400000,
      0xa0000: 0x40,
      0xb0000: 0x80001000,
      0xc0000: 0x80400040,
      0xd0000: 0x1040,
      0xe0000: 0x1000,
      0xf0000: 0x80401040,
      0x8000: 0x80001040,
      0x18000: 0x40,
      0x28000: 0x80400040,
      0x38000: 0x80001000,
      0x48000: 0x401000,
      0x58000: 0x80401040,
      0x68000: 0x0,
      0x78000: 0x80400000,
      0x88000: 0x1000,
      0x98000: 0x80401000,
      0xa8000: 0x400000,
      0xb8000: 0x1040,
      0xc8000: 0x80000000,
      0xd8000: 0x400040,
      0xe8000: 0x401040,
      0xf8000: 0x80000040,
      0x100000: 0x400040,
      0x110000: 0x401000,
      0x120000: 0x80000040,
      0x130000: 0x0,
      0x140000: 0x1040,
      0x150000: 0x80400040,
      0x160000: 0x80401000,
      0x170000: 0x80001040,
      0x180000: 0x80401040,
      0x190000: 0x80000000,
      0x1a0000: 0x80400000,
      0x1b0000: 0x401040,
      0x1c0000: 0x80001000,
      0x1d0000: 0x400000,
      0x1e0000: 0x40,
      0x1f0000: 0x1000,
      0x108000: 0x80400000,
      0x118000: 0x80401040,
      0x128000: 0x0,
      0x138000: 0x401000,
      0x148000: 0x400040,
      0x158000: 0x80000000,
      0x168000: 0x80001040,
      0x178000: 0x40,
      0x188000: 0x80000040,
      0x198000: 0x1000,
      0x1a8000: 0x80001000,
      0x1b8000: 0x80400040,
      0x1c8000: 0x1040,
      0x1d8000: 0x80401000,
      0x1e8000: 0x400000,
      0x1f8000: 0x401040
    }, {
      0x0: 0x80,
      0x1000: 0x1040000,
      0x2000: 0x40000,
      0x3000: 0x20000000,
      0x4000: 0x20040080,
      0x5000: 0x1000080,
      0x6000: 0x21000080,
      0x7000: 0x40080,
      0x8000: 0x1000000,
      0x9000: 0x20040000,
      0xa000: 0x20000080,
      0xb000: 0x21040080,
      0xc000: 0x21040000,
      0xd000: 0x0,
      0xe000: 0x1040080,
      0xf000: 0x21000000,
      0x800: 0x1040080,
      0x1800: 0x21000080,
      0x2800: 0x80,
      0x3800: 0x1040000,
      0x4800: 0x40000,
      0x5800: 0x20040080,
      0x6800: 0x21040000,
      0x7800: 0x20000000,
      0x8800: 0x20040000,
      0x9800: 0x0,
      0xa800: 0x21040080,
      0xb800: 0x1000080,
      0xc800: 0x20000080,
      0xd800: 0x21000000,
      0xe800: 0x1000000,
      0xf800: 0x40080,
      0x10000: 0x40000,
      0x11000: 0x80,
      0x12000: 0x20000000,
      0x13000: 0x21000080,
      0x14000: 0x1000080,
      0x15000: 0x21040000,
      0x16000: 0x20040080,
      0x17000: 0x1000000,
      0x18000: 0x21040080,
      0x19000: 0x21000000,
      0x1a000: 0x1040000,
      0x1b000: 0x20040000,
      0x1c000: 0x40080,
      0x1d000: 0x20000080,
      0x1e000: 0x0,
      0x1f000: 0x1040080,
      0x10800: 0x21000080,
      0x11800: 0x1000000,
      0x12800: 0x1040000,
      0x13800: 0x20040080,
      0x14800: 0x20000000,
      0x15800: 0x1040080,
      0x16800: 0x80,
      0x17800: 0x21040000,
      0x18800: 0x40080,
      0x19800: 0x21040080,
      0x1a800: 0x0,
      0x1b800: 0x21000000,
      0x1c800: 0x1000080,
      0x1d800: 0x40000,
      0x1e800: 0x20040000,
      0x1f800: 0x20000080
    }, {
      0x0: 0x10000008,
      0x100: 0x2000,
      0x200: 0x10200000,
      0x300: 0x10202008,
      0x400: 0x10002000,
      0x500: 0x200000,
      0x600: 0x200008,
      0x700: 0x10000000,
      0x800: 0x0,
      0x900: 0x10002008,
      0xa00: 0x202000,
      0xb00: 0x8,
      0xc00: 0x10200008,
      0xd00: 0x202008,
      0xe00: 0x2008,
      0xf00: 0x10202000,
      0x80: 0x10200000,
      0x180: 0x10202008,
      0x280: 0x8,
      0x380: 0x200000,
      0x480: 0x202008,
      0x580: 0x10000008,
      0x680: 0x10002000,
      0x780: 0x2008,
      0x880: 0x200008,
      0x980: 0x2000,
      0xa80: 0x10002008,
      0xb80: 0x10200008,
      0xc80: 0x0,
      0xd80: 0x10202000,
      0xe80: 0x202000,
      0xf80: 0x10000000,
      0x1000: 0x10002000,
      0x1100: 0x10200008,
      0x1200: 0x10202008,
      0x1300: 0x2008,
      0x1400: 0x200000,
      0x1500: 0x10000000,
      0x1600: 0x10000008,
      0x1700: 0x202000,
      0x1800: 0x202008,
      0x1900: 0x0,
      0x1a00: 0x8,
      0x1b00: 0x10200000,
      0x1c00: 0x2000,
      0x1d00: 0x10002008,
      0x1e00: 0x10202000,
      0x1f00: 0x200008,
      0x1080: 0x8,
      0x1180: 0x202000,
      0x1280: 0x200000,
      0x1380: 0x10000008,
      0x1480: 0x10002000,
      0x1580: 0x2008,
      0x1680: 0x10202008,
      0x1780: 0x10200000,
      0x1880: 0x10202000,
      0x1980: 0x10200008,
      0x1a80: 0x2000,
      0x1b80: 0x202008,
      0x1c80: 0x200008,
      0x1d80: 0x0,
      0x1e80: 0x10000000,
      0x1f80: 0x10002008
    }, {
      0x0: 0x100000,
      0x10: 0x2000401,
      0x20: 0x400,
      0x30: 0x100401,
      0x40: 0x2100401,
      0x50: 0x0,
      0x60: 0x1,
      0x70: 0x2100001,
      0x80: 0x2000400,
      0x90: 0x100001,
      0xa0: 0x2000001,
      0xb0: 0x2100400,
      0xc0: 0x2100000,
      0xd0: 0x401,
      0xe0: 0x100400,
      0xf0: 0x2000000,
      0x8: 0x2100001,
      0x18: 0x0,
      0x28: 0x2000401,
      0x38: 0x2100400,
      0x48: 0x100000,
      0x58: 0x2000001,
      0x68: 0x2000000,
      0x78: 0x401,
      0x88: 0x100401,
      0x98: 0x2000400,
      0xa8: 0x2100000,
      0xb8: 0x100001,
      0xc8: 0x400,
      0xd8: 0x2100401,
      0xe8: 0x1,
      0xf8: 0x100400,
      0x100: 0x2000000,
      0x110: 0x100000,
      0x120: 0x2000401,
      0x130: 0x2100001,
      0x140: 0x100001,
      0x150: 0x2000400,
      0x160: 0x2100400,
      0x170: 0x100401,
      0x180: 0x401,
      0x190: 0x2100401,
      0x1a0: 0x100400,
      0x1b0: 0x1,
      0x1c0: 0x0,
      0x1d0: 0x2100000,
      0x1e0: 0x2000001,
      0x1f0: 0x400,
      0x108: 0x100400,
      0x118: 0x2000401,
      0x128: 0x2100001,
      0x138: 0x1,
      0x148: 0x2000000,
      0x158: 0x100000,
      0x168: 0x401,
      0x178: 0x2100400,
      0x188: 0x2000001,
      0x198: 0x2100000,
      0x1a8: 0x0,
      0x1b8: 0x2100401,
      0x1c8: 0x100401,
      0x1d8: 0x400,
      0x1e8: 0x2000400,
      0x1f8: 0x100001
    }, {
      0x0: 0x8000820,
      0x1: 0x20000,
      0x2: 0x8000000,
      0x3: 0x20,
      0x4: 0x20020,
      0x5: 0x8020820,
      0x6: 0x8020800,
      0x7: 0x800,
      0x8: 0x8020000,
      0x9: 0x8000800,
      0xa: 0x20800,
      0xb: 0x8020020,
      0xc: 0x820,
      0xd: 0x0,
      0xe: 0x8000020,
      0xf: 0x20820,
      0x80000000: 0x800,
      0x80000001: 0x8020820,
      0x80000002: 0x8000820,
      0x80000003: 0x8000000,
      0x80000004: 0x8020000,
      0x80000005: 0x20800,
      0x80000006: 0x20820,
      0x80000007: 0x20,
      0x80000008: 0x8000020,
      0x80000009: 0x820,
      0x8000000a: 0x20020,
      0x8000000b: 0x8020800,
      0x8000000c: 0x0,
      0x8000000d: 0x8020020,
      0x8000000e: 0x8000800,
      0x8000000f: 0x20000,
      0x10: 0x20820,
      0x11: 0x8020800,
      0x12: 0x20,
      0x13: 0x800,
      0x14: 0x8000800,
      0x15: 0x8000020,
      0x16: 0x8020020,
      0x17: 0x20000,
      0x18: 0x0,
      0x19: 0x20020,
      0x1a: 0x8020000,
      0x1b: 0x8000820,
      0x1c: 0x8020820,
      0x1d: 0x20800,
      0x1e: 0x820,
      0x1f: 0x8000000,
      0x80000010: 0x20000,
      0x80000011: 0x800,
      0x80000012: 0x8020020,
      0x80000013: 0x20820,
      0x80000014: 0x20,
      0x80000015: 0x8020000,
      0x80000016: 0x8000000,
      0x80000017: 0x8000820,
      0x80000018: 0x8020820,
      0x80000019: 0x8000020,
      0x8000001a: 0x8000800,
      0x8000001b: 0x0,
      0x8000001c: 0x20800,
      0x8000001d: 0x820,
      0x8000001e: 0x20020,
      0x8000001f: 0x8020800
    }];

    // Masks that select the SBOX input
    var SBOX_MASK = [0xf8000001, 0x1f800000, 0x01f80000, 0x001f8000, 0x0001f800, 0x00001f80, 0x000001f8, 0x8000001f];

    /**
     * DES block cipher algorithm.
     */
    var DES = C_algo.DES = BlockCipher.extend({
      _doReset: function _doReset() {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;

        // Select 56 bits according to PC1
        var keyBits = [];
        for (var i = 0; i < 56; i++) {
          var keyBitPos = PC1[i] - 1;
          keyBits[i] = keyWords[keyBitPos >>> 5] >>> 31 - keyBitPos % 32 & 1;
        }

        // Assemble 16 subkeys
        var subKeys = this._subKeys = [];
        for (var nSubKey = 0; nSubKey < 16; nSubKey++) {
          // Create subkey
          var subKey = subKeys[nSubKey] = [];

          // Shortcut
          var bitShift = BIT_SHIFTS[nSubKey];

          // Select 48 bits according to PC2
          for (var i = 0; i < 24; i++) {
            // Select from the left 28 key bits
            subKey[i / 6 | 0] |= keyBits[(PC2[i] - 1 + bitShift) % 28] << 31 - i % 6;

            // Select from the right 28 key bits
            subKey[4 + (i / 6 | 0)] |= keyBits[28 + (PC2[i + 24] - 1 + bitShift) % 28] << 31 - i % 6;
          }

          // Since each subkey is applied to an expanded 32-bit input,
          // the subkey can be broken into 8 values scaled to 32-bits,
          // which allows the key to be used without expansion
          subKey[0] = subKey[0] << 1 | subKey[0] >>> 31;
          for (var i = 1; i < 7; i++) {
            subKey[i] = subKey[i] >>> (i - 1) * 4 + 3;
          }
          subKey[7] = subKey[7] << 5 | subKey[7] >>> 27;
        }

        // Compute inverse subkeys
        var invSubKeys = this._invSubKeys = [];
        for (var i = 0; i < 16; i++) {
          invSubKeys[i] = subKeys[15 - i];
        }
      },
      encryptBlock: function encryptBlock(M, offset) {
        this._doCryptBlock(M, offset, this._subKeys);
      },
      decryptBlock: function decryptBlock(M, offset) {
        this._doCryptBlock(M, offset, this._invSubKeys);
      },
      _doCryptBlock: function _doCryptBlock(M, offset, subKeys) {
        // Get input
        this._lBlock = M[offset];
        this._rBlock = M[offset + 1];

        // Initial permutation
        exchangeLR.call(this, 4, 0x0f0f0f0f);
        exchangeLR.call(this, 16, 0x0000ffff);
        exchangeRL.call(this, 2, 0x33333333);
        exchangeRL.call(this, 8, 0x00ff00ff);
        exchangeLR.call(this, 1, 0x55555555);

        // Rounds
        for (var round = 0; round < 16; round++) {
          // Shortcuts
          var subKey = subKeys[round];
          var lBlock = this._lBlock;
          var rBlock = this._rBlock;

          // Feistel function
          var f = 0;
          for (var i = 0; i < 8; i++) {
            f |= SBOX_P[i][((rBlock ^ subKey[i]) & SBOX_MASK[i]) >>> 0];
          }
          this._lBlock = rBlock;
          this._rBlock = lBlock ^ f;
        }

        // Undo swap from last round
        var t = this._lBlock;
        this._lBlock = this._rBlock;
        this._rBlock = t;

        // Final permutation
        exchangeLR.call(this, 1, 0x55555555);
        exchangeRL.call(this, 8, 0x00ff00ff);
        exchangeRL.call(this, 2, 0x33333333);
        exchangeLR.call(this, 16, 0x0000ffff);
        exchangeLR.call(this, 4, 0x0f0f0f0f);

        // Set output
        M[offset] = this._lBlock;
        M[offset + 1] = this._rBlock;
      },
      keySize: 64 / 32,
      ivSize: 64 / 32,
      blockSize: 64 / 32
    });

    // Swap bits across the left and right words
    function exchangeLR(offset, mask) {
      var t = (this._lBlock >>> offset ^ this._rBlock) & mask;
      this._rBlock ^= t;
      this._lBlock ^= t << offset;
    }
    function exchangeRL(offset, mask) {
      var t = (this._rBlock >>> offset ^ this._lBlock) & mask;
      this._lBlock ^= t;
      this._rBlock ^= t << offset;
    }

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.DES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.DES.decrypt(ciphertext, key, cfg);
     */
    C.DES = BlockCipher._createHelper(DES);

    /**
     * Triple-DES block cipher algorithm.
     */
    var TripleDES = C_algo.TripleDES = BlockCipher.extend({
      _doReset: function _doReset() {
        // Shortcuts
        var key = this._key;
        var keyWords = key.words;
        // Make sure the key length is valid (64, 128 or >= 192 bit)
        if (keyWords.length !== 2 && keyWords.length !== 4 && keyWords.length < 6) {
          throw new Error('Invalid key length - 3DES requires the key length to be 64, 128, 192 or >192.');
        }

        // Extend the key according to the keying options defined in 3DES standard
        var key1 = keyWords.slice(0, 2);
        var key2 = keyWords.length < 4 ? keyWords.slice(0, 2) : keyWords.slice(2, 4);
        var key3 = keyWords.length < 6 ? keyWords.slice(0, 2) : keyWords.slice(4, 6);

        // Create DES instances
        this._des1 = DES.createEncryptor(WordArray.create(key1));
        this._des2 = DES.createEncryptor(WordArray.create(key2));
        this._des3 = DES.createEncryptor(WordArray.create(key3));
      },
      encryptBlock: function encryptBlock(M, offset) {
        this._des1.encryptBlock(M, offset);
        this._des2.decryptBlock(M, offset);
        this._des3.encryptBlock(M, offset);
      },
      decryptBlock: function decryptBlock(M, offset) {
        this._des3.decryptBlock(M, offset);
        this._des2.encryptBlock(M, offset);
        this._des1.decryptBlock(M, offset);
      },
      keySize: 192 / 32,
      ivSize: 64 / 32,
      blockSize: 64 / 32
    });

    /**
     * Shortcut functions to the cipher's object interface.
     *
     * @example
     *
     *     var ciphertext = CryptoJS.TripleDES.encrypt(message, key, cfg);
     *     var plaintext  = CryptoJS.TripleDES.decrypt(ciphertext, key, cfg);
     */
    C.TripleDES = BlockCipher._createHelper(TripleDES);
  })();
  return CryptoJS.TripleDES;
});

/***/ }),

/***/ 18909:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(20731);
;
(function (root, factory) {
  if (true) {
    // CommonJS
    module.exports = exports = factory(__webpack_require__(48352));
  } else {}
})(void 0, function (CryptoJS) {
  (function (undefined) {
    // Shortcuts
    var C = CryptoJS;
    var C_lib = C.lib;
    var Base = C_lib.Base;
    var X32WordArray = C_lib.WordArray;

    /**
     * x64 namespace.
     */
    var C_x64 = C.x64 = {};

    /**
     * A 64-bit word.
     */
    var X64Word = C_x64.Word = Base.extend({
      /**
       * Initializes a newly created 64-bit word.
       *
       * @param {number} high The high 32 bits.
       * @param {number} low The low 32 bits.
       *
       * @example
       *
       *     var x64Word = CryptoJS.x64.Word.create(0x00010203, 0x04050607);
       */
      init: function init(high, low) {
        this.high = high;
        this.low = low;
      }

      /**
       * Bitwise NOTs this word.
       *
       * @return {X64Word} A new x64-Word object after negating.
       *
       * @example
       *
       *     var negated = x64Word.not();
       */
      // not: function () {
      // var high = ~this.high;
      // var low = ~this.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise ANDs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to AND with this word.
       *
       * @return {X64Word} A new x64-Word object after ANDing.
       *
       * @example
       *
       *     var anded = x64Word.and(anotherX64Word);
       */
      // and: function (word) {
      // var high = this.high & word.high;
      // var low = this.low & word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise ORs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to OR with this word.
       *
       * @return {X64Word} A new x64-Word object after ORing.
       *
       * @example
       *
       *     var ored = x64Word.or(anotherX64Word);
       */
      // or: function (word) {
      // var high = this.high | word.high;
      // var low = this.low | word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Bitwise XORs this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to XOR with this word.
       *
       * @return {X64Word} A new x64-Word object after XORing.
       *
       * @example
       *
       *     var xored = x64Word.xor(anotherX64Word);
       */
      // xor: function (word) {
      // var high = this.high ^ word.high;
      // var low = this.low ^ word.low;

      // return X64Word.create(high, low);
      // },

      /**
       * Shifts this word n bits to the left.
       *
       * @param {number} n The number of bits to shift.
       *
       * @return {X64Word} A new x64-Word object after shifting.
       *
       * @example
       *
       *     var shifted = x64Word.shiftL(25);
       */
      // shiftL: function (n) {
      // if (n < 32) {
      // var high = (this.high << n) | (this.low >>> (32 - n));
      // var low = this.low << n;
      // } else {
      // var high = this.low << (n - 32);
      // var low = 0;
      // }

      // return X64Word.create(high, low);
      // },

      /**
       * Shifts this word n bits to the right.
       *
       * @param {number} n The number of bits to shift.
       *
       * @return {X64Word} A new x64-Word object after shifting.
       *
       * @example
       *
       *     var shifted = x64Word.shiftR(7);
       */
      // shiftR: function (n) {
      // if (n < 32) {
      // var low = (this.low >>> n) | (this.high << (32 - n));
      // var high = this.high >>> n;
      // } else {
      // var low = this.high >>> (n - 32);
      // var high = 0;
      // }

      // return X64Word.create(high, low);
      // },

      /**
       * Rotates this word n bits to the left.
       *
       * @param {number} n The number of bits to rotate.
       *
       * @return {X64Word} A new x64-Word object after rotating.
       *
       * @example
       *
       *     var rotated = x64Word.rotL(25);
       */
      // rotL: function (n) {
      // return this.shiftL(n).or(this.shiftR(64 - n));
      // },

      /**
       * Rotates this word n bits to the right.
       *
       * @param {number} n The number of bits to rotate.
       *
       * @return {X64Word} A new x64-Word object after rotating.
       *
       * @example
       *
       *     var rotated = x64Word.rotR(7);
       */
      // rotR: function (n) {
      // return this.shiftR(n).or(this.shiftL(64 - n));
      // },

      /**
       * Adds this word with the passed word.
       *
       * @param {X64Word} word The x64-Word to add with this word.
       *
       * @return {X64Word} A new x64-Word object after adding.
       *
       * @example
       *
       *     var added = x64Word.add(anotherX64Word);
       */
      // add: function (word) {
      // var low = (this.low + word.low) | 0;
      // var carry = (low >>> 0) < (this.low >>> 0) ? 1 : 0;
      // var high = (this.high + word.high + carry) | 0;

      // return X64Word.create(high, low);
      // }
    });

    /**
     * An array of 64-bit words.
     *
     * @property {Array} words The array of CryptoJS.x64.Word objects.
     * @property {number} sigBytes The number of significant bytes in this word array.
     */
    var X64WordArray = C_x64.WordArray = Base.extend({
      /**
       * Initializes a newly created word array.
       *
       * @param {Array} words (Optional) An array of CryptoJS.x64.Word objects.
       * @param {number} sigBytes (Optional) The number of significant bytes in the words.
       *
       * @example
       *
       *     var wordArray = CryptoJS.x64.WordArray.create();
       *
       *     var wordArray = CryptoJS.x64.WordArray.create([
       *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
       *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
       *     ]);
       *
       *     var wordArray = CryptoJS.x64.WordArray.create([
       *         CryptoJS.x64.Word.create(0x00010203, 0x04050607),
       *         CryptoJS.x64.Word.create(0x18191a1b, 0x1c1d1e1f)
       *     ], 10);
       */
      init: function init(words, sigBytes) {
        words = this.words = words || [];
        if (sigBytes != undefined) {
          this.sigBytes = sigBytes;
        } else {
          this.sigBytes = words.length * 8;
        }
      },
      /**
       * Converts this 64-bit word array to a 32-bit word array.
       *
       * @return {CryptoJS.lib.WordArray} This word array's data as a 32-bit word array.
       *
       * @example
       *
       *     var x32WordArray = x64WordArray.toX32();
       */
      toX32: function toX32() {
        // Shortcuts
        var x64Words = this.words;
        var x64WordsLength = x64Words.length;

        // Convert
        var x32Words = [];
        for (var i = 0; i < x64WordsLength; i++) {
          var x64Word = x64Words[i];
          x32Words.push(x64Word.high);
          x32Words.push(x64Word.low);
        }
        return X32WordArray.create(x32Words, this.sigBytes);
      },
      /**
       * Creates a copy of this word array.
       *
       * @return {X64WordArray} The clone.
       *
       * @example
       *
       *     var clone = x64WordArray.clone();
       */
      clone: function clone() {
        var clone = Base.clone.call(this);

        // Clone "words" array
        var words = clone.words = this.words.slice(0);

        // Clone each X64Word object
        var wordsLength = words.length;
        for (var i = 0; i < wordsLength; i++) {
          words[i] = words[i].clone();
        }
        return clone;
      }
    });
  })();
  return CryptoJS;
});

/***/ }),

/***/ 18645:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(65292);
__webpack_require__(73844);
__webpack_require__(35877);
__webpack_require__(38178);
__webpack_require__(11765);
__webpack_require__(24863);
__webpack_require__(43448);
__webpack_require__(63956);
__webpack_require__(71950);
__webpack_require__(68067);
__webpack_require__(57114);
__webpack_require__(42437);
__webpack_require__(69330);
__webpack_require__(81755);
__webpack_require__(20731);
__webpack_require__(14032);
__webpack_require__(61726);
__webpack_require__(58281);
__webpack_require__(6422);
__webpack_require__(94712);
function _createForOfIteratorHelperLoose(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (t) return (t = t.call(r)).next.bind(t); if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var o = 0; return function () { return o >= r.length ? { done: !0 } : { done: !1, value: r[o++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _regeneratorRuntime() { "use strict"; /*! regenerator-runtime -- Copyright (c) 2014-present, Facebook, Inc. -- license (MIT): https://github.com/facebook/regenerator/blob/main/LICENSE */ _regeneratorRuntime = function _regeneratorRuntime() { return e; }; var t, e = {}, r = Object.prototype, n = r.hasOwnProperty, o = Object.defineProperty || function (t, e, r) { t[e] = r.value; }, i = "function" == typeof Symbol ? Symbol : {}, a = i.iterator || "@@iterator", c = i.asyncIterator || "@@asyncIterator", u = i.toStringTag || "@@toStringTag"; function define(t, e, r) { return Object.defineProperty(t, e, { value: r, enumerable: !0, configurable: !0, writable: !0 }), t[e]; } try { define({}, ""); } catch (t) { define = function define(t, e, r) { return t[e] = r; }; } function wrap(t, e, r, n) { var i = e && e.prototype instanceof Generator ? e : Generator, a = Object.create(i.prototype), c = new Context(n || []); return o(a, "_invoke", { value: makeInvokeMethod(t, r, c) }), a; } function tryCatch(t, e, r) { try { return { type: "normal", arg: t.call(e, r) }; } catch (t) { return { type: "throw", arg: t }; } } e.wrap = wrap; var h = "suspendedStart", l = "suspendedYield", f = "executing", s = "completed", y = {}; function Generator() {} function GeneratorFunction() {} function GeneratorFunctionPrototype() {} var p = {}; define(p, a, function () { return this; }); var d = Object.getPrototypeOf, v = d && d(d(values([]))); v && v !== r && n.call(v, a) && (p = v); var g = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(p); function defineIteratorMethods(t) { ["next", "throw", "return"].forEach(function (e) { define(t, e, function (t) { return this._invoke(e, t); }); }); } function AsyncIterator(t, e) { function invoke(r, o, i, a) { var c = tryCatch(t[r], t, o); if ("throw" !== c.type) { var u = c.arg, h = u.value; return h && "object" == typeof h && n.call(h, "__await") ? e.resolve(h.__await).then(function (t) { invoke("next", t, i, a); }, function (t) { invoke("throw", t, i, a); }) : e.resolve(h).then(function (t) { u.value = t, i(u); }, function (t) { return invoke("throw", t, i, a); }); } a(c.arg); } var r; o(this, "_invoke", { value: function value(t, n) { function callInvokeWithMethodAndArg() { return new e(function (e, r) { invoke(t, n, e, r); }); } return r = r ? r.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg(); } }); } function makeInvokeMethod(e, r, n) { var o = h; return function (i, a) { if (o === f) throw Error("Generator is already running"); if (o === s) { if ("throw" === i) throw a; return { value: t, done: !0 }; } for (n.method = i, n.arg = a;;) { var c = n.delegate; if (c) { var u = maybeInvokeDelegate(c, n); if (u) { if (u === y) continue; return u; } } if ("next" === n.method) n.sent = n._sent = n.arg;else if ("throw" === n.method) { if (o === h) throw o = s, n.arg; n.dispatchException(n.arg); } else "return" === n.method && n.abrupt("return", n.arg); o = f; var p = tryCatch(e, r, n); if ("normal" === p.type) { if (o = n.done ? s : l, p.arg === y) continue; return { value: p.arg, done: n.done }; } "throw" === p.type && (o = s, n.method = "throw", n.arg = p.arg); } }; } function maybeInvokeDelegate(e, r) { var n = r.method, o = e.iterator[n]; if (o === t) return r.delegate = null, "throw" === n && e.iterator.return && (r.method = "return", r.arg = t, maybeInvokeDelegate(e, r), "throw" === r.method) || "return" !== n && (r.method = "throw", r.arg = new TypeError("The iterator does not provide a '" + n + "' method")), y; var i = tryCatch(o, e.iterator, r.arg); if ("throw" === i.type) return r.method = "throw", r.arg = i.arg, r.delegate = null, y; var a = i.arg; return a ? a.done ? (r[e.resultName] = a.value, r.next = e.nextLoc, "return" !== r.method && (r.method = "next", r.arg = t), r.delegate = null, y) : a : (r.method = "throw", r.arg = new TypeError("iterator result is not an object"), r.delegate = null, y); } function pushTryEntry(t) { var e = { tryLoc: t[0] }; 1 in t && (e.catchLoc = t[1]), 2 in t && (e.finallyLoc = t[2], e.afterLoc = t[3]), this.tryEntries.push(e); } function resetTryEntry(t) { var e = t.completion || {}; e.type = "normal", delete e.arg, t.completion = e; } function Context(t) { this.tryEntries = [{ tryLoc: "root" }], t.forEach(pushTryEntry, this), this.reset(!0); } function values(e) { if (e || "" === e) { var r = e[a]; if (r) return r.call(e); if ("function" == typeof e.next) return e; if (!isNaN(e.length)) { var o = -1, i = function next() { for (; ++o < e.length;) if (n.call(e, o)) return next.value = e[o], next.done = !1, next; return next.value = t, next.done = !0, next; }; return i.next = i; } } throw new TypeError(typeof e + " is not iterable"); } return GeneratorFunction.prototype = GeneratorFunctionPrototype, o(g, "constructor", { value: GeneratorFunctionPrototype, configurable: !0 }), o(GeneratorFunctionPrototype, "constructor", { value: GeneratorFunction, configurable: !0 }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, u, "GeneratorFunction"), e.isGeneratorFunction = function (t) { var e = "function" == typeof t && t.constructor; return !!e && (e === GeneratorFunction || "GeneratorFunction" === (e.displayName || e.name)); }, e.mark = function (t) { return Object.setPrototypeOf ? Object.setPrototypeOf(t, GeneratorFunctionPrototype) : (t.__proto__ = GeneratorFunctionPrototype, define(t, u, "GeneratorFunction")), t.prototype = Object.create(g), t; }, e.awrap = function (t) { return { __await: t }; }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, c, function () { return this; }), e.AsyncIterator = AsyncIterator, e.async = function (t, r, n, o, i) { void 0 === i && (i = Promise); var a = new AsyncIterator(wrap(t, r, n, o), i); return e.isGeneratorFunction(r) ? a : a.next().then(function (t) { return t.done ? t.value : a.next(); }); }, defineIteratorMethods(g), define(g, u, "Generator"), define(g, a, function () { return this; }), define(g, "toString", function () { return "[object Generator]"; }), e.keys = function (t) { var e = Object(t), r = []; for (var n in e) r.push(n); return r.reverse(), function next() { for (; r.length;) { var t = r.pop(); if (t in e) return next.value = t, next.done = !1, next; } return next.done = !0, next; }; }, e.values = values, Context.prototype = { constructor: Context, reset: function reset(e) { if (this.prev = 0, this.next = 0, this.sent = this._sent = t, this.done = !1, this.delegate = null, this.method = "next", this.arg = t, this.tryEntries.forEach(resetTryEntry), !e) for (var r in this) "t" === r.charAt(0) && n.call(this, r) && !isNaN(+r.slice(1)) && (this[r] = t); }, stop: function stop() { this.done = !0; var t = this.tryEntries[0].completion; if ("throw" === t.type) throw t.arg; return this.rval; }, dispatchException: function dispatchException(e) { if (this.done) throw e; var r = this; function handle(n, o) { return a.type = "throw", a.arg = e, r.next = n, o && (r.method = "next", r.arg = t), !!o; } for (var o = this.tryEntries.length - 1; o >= 0; --o) { var i = this.tryEntries[o], a = i.completion; if ("root" === i.tryLoc) return handle("end"); if (i.tryLoc <= this.prev) { var c = n.call(i, "catchLoc"), u = n.call(i, "finallyLoc"); if (c && u) { if (this.prev < i.catchLoc) return handle(i.catchLoc, !0); if (this.prev < i.finallyLoc) return handle(i.finallyLoc); } else if (c) { if (this.prev < i.catchLoc) return handle(i.catchLoc, !0); } else { if (!u) throw Error("try statement without catch or finally"); if (this.prev < i.finallyLoc) return handle(i.finallyLoc); } } } }, abrupt: function abrupt(t, e) { for (var r = this.tryEntries.length - 1; r >= 0; --r) { var o = this.tryEntries[r]; if (o.tryLoc <= this.prev && n.call(o, "finallyLoc") && this.prev < o.finallyLoc) { var i = o; break; } } i && ("break" === t || "continue" === t) && i.tryLoc <= e && e <= i.finallyLoc && (i = null); var a = i ? i.completion : {}; return a.type = t, a.arg = e, i ? (this.method = "next", this.next = i.finallyLoc, y) : this.complete(a); }, complete: function complete(t, e) { if ("throw" === t.type) throw t.arg; return "break" === t.type || "continue" === t.type ? this.next = t.arg : "return" === t.type ? (this.rval = this.arg = t.arg, this.method = "return", this.next = "end") : "normal" === t.type && e && (this.next = e), y; }, finish: function finish(t) { for (var e = this.tryEntries.length - 1; e >= 0; --e) { var r = this.tryEntries[e]; if (r.finallyLoc === t) return this.complete(r.completion, r.afterLoc), resetTryEntry(r), y; } }, catch: function _catch(t) { for (var e = this.tryEntries.length - 1; e >= 0; --e) { var r = this.tryEntries[e]; if (r.tryLoc === t) { var n = r.completion; if ("throw" === n.type) { var o = n.arg; resetTryEntry(r); } return o; } } throw Error("illegal catch attempt"); }, delegateYield: function delegateYield(e, r, n) { return this.delegate = { iterator: values(e), resultName: r, nextLoc: n }, "next" === this.method && (this.arg = t), y; } }, e; }
var INITIAL_STATE = 1;
var FAIL_STATE = 0;
/**
 * A StateMachine represents a deterministic finite automaton.
 * It can perform matches over a sequence of values, similar to a regular expression.
 */
var StateMachine = /*#__PURE__*/function () {
  function StateMachine(dfa) {
    this.stateTable = dfa.stateTable;
    this.accepting = dfa.accepting;
    this.tags = dfa.tags;
  }
  /**
   * Returns an iterable object that yields pattern matches over the input sequence.
   * Matches are of the form [startIndex, endIndex, tags].
   */
  var _proto = StateMachine.prototype;
  _proto.match = function match(str) {
    var _ref;
    var self = this;
    return _ref = {}, _ref[Symbol.iterator] = /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
      var state, startRun, lastAccepting, lastState, p, c;
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            state = INITIAL_STATE;
            startRun = null;
            lastAccepting = null;
            lastState = null;
            p = 0;
          case 5:
            if (!(p < str.length)) {
              _context.next = 21;
              break;
            }
            c = str[p];
            lastState = state;
            state = self.stateTable[state][c];
            if (!(state === FAIL_STATE)) {
              _context.next = 15;
              break;
            }
            if (!(startRun != null && lastAccepting != null && lastAccepting >= startRun)) {
              _context.next = 13;
              break;
            }
            _context.next = 13;
            return [startRun, lastAccepting, self.tags[lastState]];
          case 13:
            // reset the state as if we started over from the initial state

            state = self.stateTable[INITIAL_STATE][c];
            startRun = null;
          case 15:
            // start a run if not in the failure state

            if (state !== FAIL_STATE && startRun == null) {
              startRun = p;
            } // if accepting, mark the potential match end

            if (self.accepting[state]) {
              lastAccepting = p;
            } // reset the state to the initial state if we get into the failure state

            if (state === FAIL_STATE) {
              state = INITIAL_STATE;
            }
          case 18:
            p++;
            _context.next = 5;
            break;
          case 21:
            if (!(startRun != null && lastAccepting != null && lastAccepting >= startRun)) {
              _context.next = 24;
              break;
            }
            _context.next = 24;
            return [startRun, lastAccepting, self.tags[state]];
          case 24:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }), _ref;
  }
  /**
   * For each match over the input sequence, action functions matching
   * the tag definitions in the input pattern are called with the startIndex,
   * endIndex, and sub-match sequence.
   */;
  _proto.apply = function apply(str, actions) {
    for (var _iterator = _createForOfIteratorHelperLoose(this.match(str)), _step; !(_step = _iterator()).done;) {
      var _step$value = _step.value,
        start = _step$value[0],
        end = _step$value[1],
        tags = _step$value[2];
      for (var _iterator2 = _createForOfIteratorHelperLoose(tags), _step2; !(_step2 = _iterator2()).done;) {
        var tag = _step2.value;
        if (typeof actions[tag] === 'function') {
          actions[tag](start, end, str.slice(start, end + 1));
        }
      }
    }
  };
  return StateMachine;
}();
module.exports = StateMachine;

/***/ }),

/***/ 84695:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


__webpack_require__(58028);
__webpack_require__(20731);
__webpack_require__(14032);
__webpack_require__(68067);
/*
 * MIT LICENSE
 * Copyright (c) 2011 Devon Govett
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify, merge,
 * publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons
 * to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all copies or
 * substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
 * BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
 * DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

var fs = __webpack_require__(48181);
var zlib = __webpack_require__(6729);
module.exports = /*#__PURE__*/function () {
  function PNG(data) {
    var i;
    this.data = data;
    this.pos = 8; // Skip the default header

    this.palette = [];
    this.imgData = [];
    this.transparency = {};
    this.text = {};
    while (true) {
      var chunkSize = this.readUInt32();
      var section = '';
      for (i = 0; i < 4; i++) {
        section += String.fromCharCode(this.data[this.pos++]);
      }
      switch (section) {
        case 'IHDR':
          // we can grab  interesting values from here (like width, height, etc)
          this.width = this.readUInt32();
          this.height = this.readUInt32();
          this.bits = this.data[this.pos++];
          this.colorType = this.data[this.pos++];
          this.compressionMethod = this.data[this.pos++];
          this.filterMethod = this.data[this.pos++];
          this.interlaceMethod = this.data[this.pos++];
          break;
        case 'PLTE':
          this.palette = this.read(chunkSize);
          break;
        case 'IDAT':
          for (i = 0; i < chunkSize; i++) {
            this.imgData.push(this.data[this.pos++]);
          }
          break;
        case 'tRNS':
          // This chunk can only occur once and it must occur after the
          // PLTE chunk and before the IDAT chunk.
          this.transparency = {};
          switch (this.colorType) {
            case 3:
              // Indexed color, RGB. Each byte in this chunk is an alpha for
              // the palette index in the PLTE ("palette") chunk up until the
              // last non-opaque entry. Set up an array, stretching over all
              // palette entries which will be 0 (opaque) or 1 (transparent).
              this.transparency.indexed = this.read(chunkSize);
              var short = 255 - this.transparency.indexed.length;
              if (short > 0) {
                for (i = 0; i < short; i++) {
                  this.transparency.indexed.push(255);
                }
              }
              break;
            case 0:
              // Greyscale. Corresponding to entries in the PLTE chunk.
              // Grey is two bytes, range 0 .. (2 ^ bit-depth) - 1
              this.transparency.grayscale = this.read(chunkSize)[0];
              break;
            case 2:
              // True color with proper alpha channel.
              this.transparency.rgb = this.read(chunkSize);
              break;
          }
          break;
        case 'tEXt':
          var text = this.read(chunkSize);
          var index = text.indexOf(0);
          var key = String.fromCharCode.apply(String, text.slice(0, index));
          this.text[key] = String.fromCharCode.apply(String, text.slice(index + 1));
          break;
        case 'IEND':
          // we've got everything we need!
          switch (this.colorType) {
            case 0:
            case 3:
            case 4:
              this.colors = 1;
              break;
            case 2:
            case 6:
              this.colors = 3;
              break;
          }
          this.hasAlphaChannel = [4, 6].includes(this.colorType);
          var colors = this.colors + (this.hasAlphaChannel ? 1 : 0);
          this.pixelBitlength = this.bits * colors;
          switch (this.colors) {
            case 1:
              this.colorSpace = 'DeviceGray';
              break;
            case 3:
              this.colorSpace = 'DeviceRGB';
              break;
          }
          this.imgData = new Buffer(this.imgData);
          return;
          break;
        default:
          // unknown (or unimportant) section, skip it
          this.pos += chunkSize;
      }
      this.pos += 4; // Skip the CRC

      if (this.pos > this.data.length) {
        throw new Error('Incomplete or corrupt PNG file');
      }
    }
  }
  PNG.decode = function decode(path, fn) {
    return fs.readFile(path, function (err, file) {
      var png = new PNG(file);
      return png.decode(function (pixels) {
        return fn(pixels);
      });
    });
  };
  PNG.load = function load(path) {
    var file = fs.readFileSync(path);
    return new PNG(file);
  };
  var _proto = PNG.prototype;
  _proto.read = function read(bytes) {
    var result = new Array(bytes);
    for (var i = 0; i < bytes; i++) {
      result[i] = this.data[this.pos++];
    }
    return result;
  };
  _proto.readUInt32 = function readUInt32() {
    var b1 = this.data[this.pos++] << 24;
    var b2 = this.data[this.pos++] << 16;
    var b3 = this.data[this.pos++] << 8;
    var b4 = this.data[this.pos++];
    return b1 | b2 | b3 | b4;
  };
  _proto.readUInt16 = function readUInt16() {
    var b1 = this.data[this.pos++] << 8;
    var b2 = this.data[this.pos++];
    return b1 | b2;
  };
  _proto.decodePixels = function decodePixels(fn) {
    var _this = this;
    return zlib.inflate(this.imgData, function (err, data) {
      if (err) {
        throw err;
      }
      var width = _this.width,
        height = _this.height;
      var pixelBytes = _this.pixelBitlength / 8;
      var pixels = new Buffer(width * height * pixelBytes);
      var length = data.length;
      var pos = 0;
      function pass(x0, y0, dx, dy, singlePass) {
        if (singlePass === void 0) {
          singlePass = false;
        }
        var w = Math.ceil((width - x0) / dx);
        var h = Math.ceil((height - y0) / dy);
        var scanlineLength = pixelBytes * w;
        var buffer = singlePass ? pixels : new Buffer(scanlineLength * h);
        var row = 0;
        var c = 0;
        while (row < h && pos < length) {
          var byte, col, i, left, upper;
          switch (data[pos++]) {
            case 0:
              // None
              for (i = 0; i < scanlineLength; i++) {
                buffer[c++] = data[pos++];
              }
              break;
            case 1:
              // Sub
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];
                buffer[c++] = (byte + left) % 256;
              }
              break;
            case 2:
              // Up
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                upper = row && buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                buffer[c++] = (upper + byte) % 256;
              }
              break;
            case 3:
              // Average
              for (i = 0; i < scanlineLength; i++) {
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];
                upper = row && buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                buffer[c++] = (byte + Math.floor((left + upper) / 2)) % 256;
              }
              break;
            case 4:
              // Paeth
              for (i = 0; i < scanlineLength; i++) {
                var paeth, upperLeft;
                byte = data[pos++];
                col = (i - i % pixelBytes) / pixelBytes;
                left = i < pixelBytes ? 0 : buffer[c - pixelBytes];
                if (row === 0) {
                  upper = upperLeft = 0;
                } else {
                  upper = buffer[(row - 1) * scanlineLength + col * pixelBytes + i % pixelBytes];
                  upperLeft = col && buffer[(row - 1) * scanlineLength + (col - 1) * pixelBytes + i % pixelBytes];
                }
                var p = left + upper - upperLeft;
                var pa = Math.abs(p - left);
                var pb = Math.abs(p - upper);
                var pc = Math.abs(p - upperLeft);
                if (pa <= pb && pa <= pc) {
                  paeth = left;
                } else if (pb <= pc) {
                  paeth = upper;
                } else {
                  paeth = upperLeft;
                }
                buffer[c++] = (byte + paeth) % 256;
              }
              break;
            default:
              throw new Error("Invalid filter algorithm: " + data[pos - 1]);
          }
          if (!singlePass) {
            var pixelsPos = ((y0 + row * dy) * width + x0) * pixelBytes;
            var bufferPos = row * scanlineLength;
            for (i = 0; i < w; i++) {
              for (var j = 0; j < pixelBytes; j++) pixels[pixelsPos++] = buffer[bufferPos++];
              pixelsPos += (dx - 1) * pixelBytes;
            }
          }
          row++;
        }
      }
      if (_this.interlaceMethod === 1) {
        /*
          1 6 4 6 2 6 4 6
          7 7 7 7 7 7 7 7
          5 6 5 6 5 6 5 6
          7 7 7 7 7 7 7 7
          3 6 4 6 3 6 4 6
          7 7 7 7 7 7 7 7
          5 6 5 6 5 6 5 6
          7 7 7 7 7 7 7 7
        */
        pass(0, 0, 8, 8); // 1
        pass(4, 0, 8, 8); // 2
        pass(0, 4, 4, 8); // 3
        pass(2, 0, 4, 4); // 4
        pass(0, 2, 2, 4); // 5
        pass(1, 0, 2, 2); // 6
        pass(0, 1, 1, 2); // 7
      } else {
        pass(0, 0, 1, 1, true);
      }
      return fn(pixels);
    });
  };
  _proto.decodePalette = function decodePalette() {
    var palette = this.palette;
    var length = palette.length;
    var transparency = this.transparency.indexed || [];
    var ret = new Buffer(transparency.length + length);
    var pos = 0;
    var c = 0;
    for (var i = 0; i < length; i += 3) {
      var left;
      ret[pos++] = palette[i];
      ret[pos++] = palette[i + 1];
      ret[pos++] = palette[i + 2];
      ret[pos++] = (left = transparency[c++]) != null ? left : 255;
    }
    return ret;
  };
  _proto.copyToImageData = function copyToImageData(imageData, pixels) {
    var j, k;
    var colors = this.colors;
    var palette = null;
    var alpha = this.hasAlphaChannel;
    if (this.palette.length) {
      palette = this._decodedPalette || (this._decodedPalette = this.decodePalette());
      colors = 4;
      alpha = true;
    }
    var data = imageData.data || imageData;
    var length = data.length;
    var input = palette || pixels;
    var i = j = 0;
    if (colors === 1) {
      while (i < length) {
        k = palette ? pixels[i / 4] * 4 : j;
        var v = input[k++];
        data[i++] = v;
        data[i++] = v;
        data[i++] = v;
        data[i++] = alpha ? input[k++] : 255;
        j = k;
      }
    } else {
      while (i < length) {
        k = palette ? pixels[i / 4] * 4 : j;
        data[i++] = input[k++];
        data[i++] = input[k++];
        data[i++] = input[k++];
        data[i++] = alpha ? input[k++] : 255;
        j = k;
      }
    }
  };
  _proto.decode = function decode(fn) {
    var _this2 = this;
    var ret = new Buffer(this.width * this.height * 4);
    return this.decodePixels(function (pixels) {
      _this2.copyToImageData(ret, pixels);
      return fn(ret);
    });
  };
  return PNG;
}();

/***/ }),

/***/ 93143:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
/* eslint-disable node/no-deprecated-api */



__webpack_require__(41584);
__webpack_require__(81755);
__webpack_require__(8953);
__webpack_require__(14032);
__webpack_require__(56912);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(83326);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
var buffer = __webpack_require__(14598);
var Buffer = buffer.Buffer;
var safer = {};
var key;
for (key in buffer) {
  if (!buffer.hasOwnProperty(key)) continue;
  if (key === 'SlowBuffer' || key === 'Buffer') continue;
  safer[key] = buffer[key];
}
var Safer = safer.Buffer = {};
for (key in Buffer) {
  if (!Buffer.hasOwnProperty(key)) continue;
  if (key === 'allocUnsafe' || key === 'allocUnsafeSlow') continue;
  Safer[key] = Buffer[key];
}
safer.Buffer.prototype = Buffer.prototype;
if (!Safer.from || Safer.from === Uint8Array.from) {
  Safer.from = function (value, encodingOrOffset, length) {
    if (typeof value === 'number') {
      throw new TypeError('The "value" argument must not be of type number. Received type ' + typeof value);
    }
    if (value && typeof value.length === 'undefined') {
      throw new TypeError('The first argument must be one of type string, Buffer, ArrayBuffer, Array, or Array-like Object. Received type ' + typeof value);
    }
    return Buffer(value, encodingOrOffset, length);
  };
}
if (!Safer.alloc) {
  Safer.alloc = function (size, fill, encoding) {
    if (typeof size !== 'number') {
      throw new TypeError('The "size" argument must be of type number. Received type ' + typeof size);
    }
    if (size < 0 || size >= 2 * (1 << 30)) {
      throw new RangeError('The value "' + size + '" is invalid for option "size"');
    }
    var buf = Buffer(size);
    if (!fill || fill.length === 0) {
      buf.fill(0);
    } else if (typeof encoding === 'string') {
      buf.fill(fill, encoding);
    } else {
      buf.fill(fill);
    }
    return buf;
  };
}
if (!safer.kStringMaxLength) {
  try {
    safer.kStringMaxLength = process.binding('buffer').kStringMaxLength;
  } catch (e) {
    // we can't determine kStringMaxLength in environments where process.binding
    // is unsupported, so let's not set it
  }
}
if (!safer.constants) {
  safer.constants = {
    MAX_LENGTH: safer.kMaxLength
  };
  if (safer.kStringMaxLength) {
    safer.constants.MAX_STRING_LENGTH = safer.kStringMaxLength;
  }
}
module.exports = safer;

/***/ }),

/***/ 24433:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(65292);
__webpack_require__(5597);
__webpack_require__(49063);
__webpack_require__(20731);
__webpack_require__(7585);
__webpack_require__(7283);
__webpack_require__(57444);
__webpack_require__(28331);
__webpack_require__(37309);
__webpack_require__(14032);
__webpack_require__(42437);
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
var _require = __webpack_require__(14598),
  Buffer = _require.Buffer;
var _require2 = __webpack_require__(63779),
  inspect = _require2.inspect;
var custom = inspect && inspect.custom || 'inspect';
function copyBuffer(src, target, offset) {
  Buffer.prototype.copy.call(src, target, offset);
}
module.exports = /*#__PURE__*/function () {
  function BufferList() {
    _classCallCheck(this, BufferList);
    this.head = null;
    this.tail = null;
    this.length = 0;
  }
  _createClass(BufferList, [{
    key: "push",
    value: function push(v) {
      var entry = {
        data: v,
        next: null
      };
      if (this.length > 0) this.tail.next = entry;else this.head = entry;
      this.tail = entry;
      ++this.length;
    }
  }, {
    key: "unshift",
    value: function unshift(v) {
      var entry = {
        data: v,
        next: this.head
      };
      if (this.length === 0) this.tail = entry;
      this.head = entry;
      ++this.length;
    }
  }, {
    key: "shift",
    value: function shift() {
      if (this.length === 0) return;
      var ret = this.head.data;
      if (this.length === 1) this.head = this.tail = null;else this.head = this.head.next;
      --this.length;
      return ret;
    }
  }, {
    key: "clear",
    value: function clear() {
      this.head = this.tail = null;
      this.length = 0;
    }
  }, {
    key: "join",
    value: function join(s) {
      if (this.length === 0) return '';
      var p = this.head;
      var ret = '' + p.data;
      while (p = p.next) ret += s + p.data;
      return ret;
    }
  }, {
    key: "concat",
    value: function concat(n) {
      if (this.length === 0) return Buffer.alloc(0);
      var ret = Buffer.allocUnsafe(n >>> 0);
      var p = this.head;
      var i = 0;
      while (p) {
        copyBuffer(p.data, ret, i);
        i += p.data.length;
        p = p.next;
      }
      return ret;
    }

    // Consumes a specified amount of bytes or characters from the buffered data.
  }, {
    key: "consume",
    value: function consume(n, hasStrings) {
      var ret;
      if (n < this.head.data.length) {
        // `slice` is the same for buffers and strings.
        ret = this.head.data.slice(0, n);
        this.head.data = this.head.data.slice(n);
      } else if (n === this.head.data.length) {
        // First chunk is a perfect match.
        ret = this.shift();
      } else {
        // Result spans more than one buffer.
        ret = hasStrings ? this._getString(n) : this._getBuffer(n);
      }
      return ret;
    }
  }, {
    key: "first",
    value: function first() {
      return this.head.data;
    }

    // Consumes a specified amount of characters from the buffered data.
  }, {
    key: "_getString",
    value: function _getString(n) {
      var p = this.head;
      var c = 1;
      var ret = p.data;
      n -= ret.length;
      while (p = p.next) {
        var str = p.data;
        var nb = n > str.length ? str.length : n;
        if (nb === str.length) ret += str;else ret += str.slice(0, n);
        n -= nb;
        if (n === 0) {
          if (nb === str.length) {
            ++c;
            if (p.next) this.head = p.next;else this.head = this.tail = null;
          } else {
            this.head = p;
            p.data = str.slice(nb);
          }
          break;
        }
        ++c;
      }
      this.length -= c;
      return ret;
    }

    // Consumes a specified amount of bytes from the buffered data.
  }, {
    key: "_getBuffer",
    value: function _getBuffer(n) {
      var ret = Buffer.allocUnsafe(n);
      var p = this.head;
      var c = 1;
      p.data.copy(ret);
      n -= p.data.length;
      while (p = p.next) {
        var buf = p.data;
        var nb = n > buf.length ? buf.length : n;
        buf.copy(ret, ret.length - n, 0, nb);
        n -= nb;
        if (n === 0) {
          if (nb === buf.length) {
            ++c;
            if (p.next) this.head = p.next;else this.head = this.tail = null;
          } else {
            this.head = p;
            p.data = buf.slice(nb);
          }
          break;
        }
        ++c;
      }
      this.length -= c;
      return ret;
    }

    // Make sure the linked list only shows the minimal necessary information.
  }, {
    key: custom,
    value: function value(_, options) {
      return inspect(this, _objectSpread(_objectSpread({}, options), {}, {
        // Only inspect one level.
        depth: 0,
        // It should not recurse.
        customInspect: false
      }));
    }
  }]);
  return BufferList;
}();

/***/ }),

/***/ 11432:
/***/ (function(module, exports, __webpack_require__) {

"use strict";


__webpack_require__(41584);
/* eslint-disable node/no-deprecated-api */
var buffer = __webpack_require__(14598);
var Buffer = buffer.Buffer;

// alternative to using Object.keys for old browsers
function copyProps(src, dst) {
  for (var key in src) {
    dst[key] = src[key];
  }
}
if (Buffer.from && Buffer.alloc && Buffer.allocUnsafe && Buffer.allocUnsafeSlow) {
  module.exports = buffer;
} else {
  // Copy properties from require('buffer')
  copyProps(buffer, exports);
  exports.Buffer = SafeBuffer;
}
function SafeBuffer(arg, encodingOrOffset, length) {
  return Buffer(arg, encodingOrOffset, length);
}

// Copy static methods from Buffer
copyProps(Buffer, SafeBuffer);
SafeBuffer.from = function (arg, encodingOrOffset, length) {
  if (typeof arg === 'number') {
    throw new TypeError('Argument must not be a number');
  }
  return Buffer(arg, encodingOrOffset, length);
};
SafeBuffer.alloc = function (size, fill, encoding) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number');
  }
  var buf = Buffer(size);
  if (fill !== undefined) {
    if (typeof encoding === 'string') {
      buf.fill(fill, encoding);
    } else {
      buf.fill(fill);
    }
  } else {
    buf.fill(0);
  }
  return buf;
};
SafeBuffer.allocUnsafe = function (size) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number');
  }
  return Buffer(size);
};
SafeBuffer.allocUnsafeSlow = function (size) {
  if (typeof size !== 'number') {
    throw new TypeError('Argument must be a number');
  }
  return buffer.SlowBuffer(size);
};

/***/ }),

/***/ 59006:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(81755);
__webpack_require__(20731);
__webpack_require__(8953);
__webpack_require__(14032);
__webpack_require__(56912);
__webpack_require__(59735);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
var inflate = __webpack_require__(3483);
var _require = __webpack_require__(84327),
  swap32LE = _require.swap32LE;

// Shift size for getting the index-1 table offset.
var SHIFT_1 = 6 + 5;

// Shift size for getting the index-2 table offset.
var SHIFT_2 = 5;

// Difference between the two shift sizes,
// for getting an index-1 offset from an index-2 offset. 6=11-5
var SHIFT_1_2 = SHIFT_1 - SHIFT_2;

// Number of index-1 entries for the BMP. 32=0x20
// This part of the index-1 table is omitted from the serialized form.
var OMITTED_BMP_INDEX_1_LENGTH = 0x10000 >> SHIFT_1;

// Number of entries in an index-2 block. 64=0x40
var INDEX_2_BLOCK_LENGTH = 1 << SHIFT_1_2;

// Mask for getting the lower bits for the in-index-2-block offset. */
var INDEX_2_MASK = INDEX_2_BLOCK_LENGTH - 1;

// Shift size for shifting left the index array values.
// Increases possible data size with 16-bit index values at the cost
// of compactability.
// This requires data blocks to be aligned by DATA_GRANULARITY.
var INDEX_SHIFT = 2;

// Number of entries in a data block. 32=0x20
var DATA_BLOCK_LENGTH = 1 << SHIFT_2;

// Mask for getting the lower bits for the in-data-block offset.
var DATA_MASK = DATA_BLOCK_LENGTH - 1;

// The part of the index-2 table for U+D800..U+DBFF stores values for
// lead surrogate code _units_ not code _points_.
// Values for lead surrogate code _points_ are indexed with this portion of the table.
// Length=32=0x20=0x400>>SHIFT_2. (There are 1024=0x400 lead surrogates.)
var LSCP_INDEX_2_OFFSET = 0x10000 >> SHIFT_2;
var LSCP_INDEX_2_LENGTH = 0x400 >> SHIFT_2;

// Count the lengths of both BMP pieces. 2080=0x820
var INDEX_2_BMP_LENGTH = LSCP_INDEX_2_OFFSET + LSCP_INDEX_2_LENGTH;

// The 2-byte UTF-8 version of the index-2 table follows at offset 2080=0x820.
// Length 32=0x20 for lead bytes C0..DF, regardless of SHIFT_2.
var UTF8_2B_INDEX_2_OFFSET = INDEX_2_BMP_LENGTH;
var UTF8_2B_INDEX_2_LENGTH = 0x800 >> 6; // U+0800 is the first code point after 2-byte UTF-8

// The index-1 table, only used for supplementary code points, at offset 2112=0x840.
// Variable length, for code points up to highStart, where the last single-value range starts.
// Maximum length 512=0x200=0x100000>>SHIFT_1.
// (For 0x100000 supplementary code points U+10000..U+10ffff.)
//
// The part of the index-2 table for supplementary code points starts
// after this index-1 table.
//
// Both the index-1 table and the following part of the index-2 table
// are omitted completely if there is only BMP data.
var INDEX_1_OFFSET = UTF8_2B_INDEX_2_OFFSET + UTF8_2B_INDEX_2_LENGTH;

// The alignment size of a data block. Also the granularity for compaction.
var DATA_GRANULARITY = 1 << INDEX_SHIFT;
var UnicodeTrie = /*#__PURE__*/function () {
  function UnicodeTrie(data) {
    var isBuffer = typeof data.readUInt32BE === 'function' && typeof data.slice === 'function';
    if (isBuffer || data instanceof Uint8Array) {
      // read binary format
      var uncompressedLength;
      if (isBuffer) {
        this.highStart = data.readUInt32LE(0);
        this.errorValue = data.readUInt32LE(4);
        uncompressedLength = data.readUInt32LE(8);
        data = data.slice(12);
      } else {
        var view = new DataView(data.buffer);
        this.highStart = view.getUint32(0, true);
        this.errorValue = view.getUint32(4, true);
        uncompressedLength = view.getUint32(8, true);
        data = data.subarray(12);
      }

      // double inflate the actual trie data
      data = inflate(data, new Uint8Array(uncompressedLength));
      data = inflate(data, new Uint8Array(uncompressedLength));

      // swap bytes from little-endian
      swap32LE(data);
      this.data = new Uint32Array(data.buffer);
    } else {
      // pre-parsed data
      var _data = data;
      this.data = _data.data;
      this.highStart = _data.highStart;
      this.errorValue = _data.errorValue;
    }
  }
  var _proto = UnicodeTrie.prototype;
  _proto.get = function get(codePoint) {
    var index;
    if (codePoint < 0 || codePoint > 0x10ffff) {
      return this.errorValue;
    }
    if (codePoint < 0xd800 || codePoint > 0xdbff && codePoint <= 0xffff) {
      // Ordinary BMP code point, excluding leading surrogates.
      // BMP uses a single level lookup.  BMP index starts at offset 0 in the index.
      // data is stored in the index array itself.
      index = (this.data[codePoint >> SHIFT_2] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }
    if (codePoint <= 0xffff) {
      // Lead Surrogate Code Point.  A Separate index section is stored for
      // lead surrogate code units and code points.
      //   The main index has the code unit data.
      //   For this function, we need the code point data.
      index = (this.data[LSCP_INDEX_2_OFFSET + (codePoint - 0xd800 >> SHIFT_2)] << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }
    if (codePoint < this.highStart) {
      // Supplemental code point, use two-level lookup.
      index = this.data[INDEX_1_OFFSET - OMITTED_BMP_INDEX_1_LENGTH + (codePoint >> SHIFT_1)];
      index = this.data[index + (codePoint >> SHIFT_2 & INDEX_2_MASK)];
      index = (index << INDEX_SHIFT) + (codePoint & DATA_MASK);
      return this.data[index];
    }
    return this.data[this.data.length - DATA_GRANULARITY];
  };
  return UnicodeTrie;
}();
module.exports = UnicodeTrie;

/***/ }),

/***/ 84327:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(81755);
__webpack_require__(8953);
__webpack_require__(14032);
__webpack_require__(56912);
__webpack_require__(59735);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
var isBigEndian = new Uint8Array(new Uint32Array([0x12345678]).buffer)[0] === 0x12;
var swap = function swap(b, n, m) {
  var i = b[n];
  b[n] = b[m];
  b[m] = i;
};
var swap32 = function swap32(array) {
  var len = array.length;
  for (var i = 0; i < len; i += 4) {
    swap(array, i, i + 3);
    swap(array, i + 1, i + 2);
  }
};
var swap32LE = function swap32LE(array) {
  if (isBigEndian) {
    swap32(array);
  }
};
module.exports = {
  swap32LE: swap32LE
};

/***/ }),

/***/ 5557:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var SVGtoPDF = __webpack_require__(64781);
module.exports = SVGtoPDF;

/***/ }),

/***/ 64781:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* module decorator */ module = __webpack_require__.nmd(module);


__webpack_require__(39081);
__webpack_require__(41584);
__webpack_require__(81755);
__webpack_require__(20731);
__webpack_require__(24863);
__webpack_require__(7283);
__webpack_require__(37309);
__webpack_require__(14032);
__webpack_require__(61726);
__webpack_require__(6422);
__webpack_require__(46467);
__webpack_require__(7851);
__webpack_require__(72095);
__webpack_require__(47259);
__webpack_require__(1083);
__webpack_require__(94712);
var SVGtoPDF = function SVGtoPDF(doc, svg, x, y, options) {
  "use strict";

  var NamedColors = {
    aliceblue: [240, 248, 255],
    antiquewhite: [250, 235, 215],
    aqua: [0, 255, 255],
    aquamarine: [127, 255, 212],
    azure: [240, 255, 255],
    beige: [245, 245, 220],
    bisque: [255, 228, 196],
    black: [0, 0, 0],
    blanchedalmond: [255, 235, 205],
    blue: [0, 0, 255],
    blueviolet: [138, 43, 226],
    brown: [165, 42, 42],
    burlywood: [222, 184, 135],
    cadetblue: [95, 158, 160],
    chartreuse: [127, 255, 0],
    chocolate: [210, 105, 30],
    coral: [255, 127, 80],
    cornflowerblue: [100, 149, 237],
    cornsilk: [255, 248, 220],
    crimson: [220, 20, 60],
    cyan: [0, 255, 255],
    darkblue: [0, 0, 139],
    darkcyan: [0, 139, 139],
    darkgoldenrod: [184, 134, 11],
    darkgray: [169, 169, 169],
    darkgrey: [169, 169, 169],
    darkgreen: [0, 100, 0],
    darkkhaki: [189, 183, 107],
    darkmagenta: [139, 0, 139],
    darkolivegreen: [85, 107, 47],
    darkorange: [255, 140, 0],
    darkorchid: [153, 50, 204],
    darkred: [139, 0, 0],
    darksalmon: [233, 150, 122],
    darkseagreen: [143, 188, 143],
    darkslateblue: [72, 61, 139],
    darkslategray: [47, 79, 79],
    darkslategrey: [47, 79, 79],
    darkturquoise: [0, 206, 209],
    darkviolet: [148, 0, 211],
    deeppink: [255, 20, 147],
    deepskyblue: [0, 191, 255],
    dimgray: [105, 105, 105],
    dimgrey: [105, 105, 105],
    dodgerblue: [30, 144, 255],
    firebrick: [178, 34, 34],
    floralwhite: [255, 250, 240],
    forestgreen: [34, 139, 34],
    fuchsia: [255, 0, 255],
    gainsboro: [220, 220, 220],
    ghostwhite: [248, 248, 255],
    gold: [255, 215, 0],
    goldenrod: [218, 165, 32],
    gray: [128, 128, 128],
    grey: [128, 128, 128],
    green: [0, 128, 0],
    greenyellow: [173, 255, 47],
    honeydew: [240, 255, 240],
    hotpink: [255, 105, 180],
    indianred: [205, 92, 92],
    indigo: [75, 0, 130],
    ivory: [255, 255, 240],
    khaki: [240, 230, 140],
    lavender: [230, 230, 250],
    lavenderblush: [255, 240, 245],
    lawngreen: [124, 252, 0],
    lemonchiffon: [255, 250, 205],
    lightblue: [173, 216, 230],
    lightcoral: [240, 128, 128],
    lightcyan: [224, 255, 255],
    lightgoldenrodyellow: [250, 250, 210],
    lightgray: [211, 211, 211],
    lightgrey: [211, 211, 211],
    lightgreen: [144, 238, 144],
    lightpink: [255, 182, 193],
    lightsalmon: [255, 160, 122],
    lightseagreen: [32, 178, 170],
    lightskyblue: [135, 206, 250],
    lightslategray: [119, 136, 153],
    lightslategrey: [119, 136, 153],
    lightsteelblue: [176, 196, 222],
    lightyellow: [255, 255, 224],
    lime: [0, 255, 0],
    limegreen: [50, 205, 50],
    linen: [250, 240, 230],
    magenta: [255, 0, 255],
    maroon: [128, 0, 0],
    mediumaquamarine: [102, 205, 170],
    mediumblue: [0, 0, 205],
    mediumorchid: [186, 85, 211],
    mediumpurple: [147, 112, 219],
    mediumseagreen: [60, 179, 113],
    mediumslateblue: [123, 104, 238],
    mediumspringgreen: [0, 250, 154],
    mediumturquoise: [72, 209, 204],
    mediumvioletred: [199, 21, 133],
    midnightblue: [25, 25, 112],
    mintcream: [245, 255, 250],
    mistyrose: [255, 228, 225],
    moccasin: [255, 228, 181],
    navajowhite: [255, 222, 173],
    navy: [0, 0, 128],
    oldlace: [253, 245, 230],
    olive: [128, 128, 0],
    olivedrab: [107, 142, 35],
    orange: [255, 165, 0],
    orangered: [255, 69, 0],
    orchid: [218, 112, 214],
    palegoldenrod: [238, 232, 170],
    palegreen: [152, 251, 152],
    paleturquoise: [175, 238, 238],
    palevioletred: [219, 112, 147],
    papayawhip: [255, 239, 213],
    peachpuff: [255, 218, 185],
    peru: [205, 133, 63],
    pink: [255, 192, 203],
    plum: [221, 160, 221],
    powderblue: [176, 224, 230],
    purple: [128, 0, 128],
    rebeccapurple: [102, 51, 153],
    red: [255, 0, 0],
    rosybrown: [188, 143, 143],
    royalblue: [65, 105, 225],
    saddlebrown: [139, 69, 19],
    salmon: [250, 128, 114],
    sandybrown: [244, 164, 96],
    seagreen: [46, 139, 87],
    seashell: [255, 245, 238],
    sienna: [160, 82, 45],
    silver: [192, 192, 192],
    skyblue: [135, 206, 235],
    slateblue: [106, 90, 205],
    slategray: [112, 128, 144],
    slategrey: [112, 128, 144],
    snow: [255, 250, 250],
    springgreen: [0, 255, 127],
    steelblue: [70, 130, 180],
    tan: [210, 180, 140],
    teal: [0, 128, 128],
    thistle: [216, 191, 216],
    tomato: [255, 99, 71],
    turquoise: [64, 224, 208],
    violet: [238, 130, 238],
    wheat: [245, 222, 179],
    white: [255, 255, 255],
    whitesmoke: [245, 245, 245],
    yellow: [255, 255, 0]
  };
  var DefaultColors = {
    black: [NamedColors.black, 1],
    white: [NamedColors.white, 1],
    transparent: [NamedColors.black, 0]
  };
  var Entities = {
    quot: 34,
    amp: 38,
    lt: 60,
    gt: 62,
    apos: 39,
    OElig: 338,
    oelig: 339,
    Scaron: 352,
    scaron: 353,
    Yuml: 376,
    circ: 710,
    tilde: 732,
    ensp: 8194,
    emsp: 8195,
    thinsp: 8201,
    zwnj: 8204,
    zwj: 8205,
    lrm: 8206,
    rlm: 8207,
    ndash: 8211,
    mdash: 8212,
    lsquo: 8216,
    rsquo: 8217,
    sbquo: 8218,
    ldquo: 8220,
    rdquo: 8221,
    bdquo: 8222,
    dagger: 8224,
    Dagger: 8225,
    permil: 8240,
    lsaquo: 8249,
    rsaquo: 8250,
    euro: 8364,
    nbsp: 160,
    iexcl: 161,
    cent: 162,
    pound: 163,
    curren: 164,
    yen: 165,
    brvbar: 166,
    sect: 167,
    uml: 168,
    copy: 169,
    ordf: 170,
    laquo: 171,
    not: 172,
    shy: 173,
    reg: 174,
    macr: 175,
    deg: 176,
    plusmn: 177,
    sup2: 178,
    sup3: 179,
    acute: 180,
    micro: 181,
    para: 182,
    middot: 183,
    cedil: 184,
    sup1: 185,
    ordm: 186,
    raquo: 187,
    frac14: 188,
    frac12: 189,
    frac34: 190,
    iquest: 191,
    Agrave: 192,
    Aacute: 193,
    Acirc: 194,
    Atilde: 195,
    Auml: 196,
    Aring: 197,
    AElig: 198,
    Ccedil: 199,
    Egrave: 200,
    Eacute: 201,
    Ecirc: 202,
    Euml: 203,
    Igrave: 204,
    Iacute: 205,
    Icirc: 206,
    Iuml: 207,
    ETH: 208,
    Ntilde: 209,
    Ograve: 210,
    Oacute: 211,
    Ocirc: 212,
    Otilde: 213,
    Ouml: 214,
    times: 215,
    Oslash: 216,
    Ugrave: 217,
    Uacute: 218,
    Ucirc: 219,
    Uuml: 220,
    Yacute: 221,
    THORN: 222,
    szlig: 223,
    agrave: 224,
    aacute: 225,
    acirc: 226,
    atilde: 227,
    auml: 228,
    aring: 229,
    aelig: 230,
    ccedil: 231,
    egrave: 232,
    eacute: 233,
    ecirc: 234,
    euml: 235,
    igrave: 236,
    iacute: 237,
    icirc: 238,
    iuml: 239,
    eth: 240,
    ntilde: 241,
    ograve: 242,
    oacute: 243,
    ocirc: 244,
    otilde: 245,
    ouml: 246,
    divide: 247,
    oslash: 248,
    ugrave: 249,
    uacute: 250,
    ucirc: 251,
    uuml: 252,
    yacute: 253,
    thorn: 254,
    yuml: 255,
    fnof: 402,
    Alpha: 913,
    Beta: 914,
    Gamma: 915,
    Delta: 916,
    Epsilon: 917,
    Zeta: 918,
    Eta: 919,
    Theta: 920,
    Iota: 921,
    Kappa: 922,
    Lambda: 923,
    Mu: 924,
    Nu: 925,
    Xi: 926,
    Omicron: 927,
    Pi: 928,
    Rho: 929,
    Sigma: 931,
    Tau: 932,
    Upsilon: 933,
    Phi: 934,
    Chi: 935,
    Psi: 936,
    Omega: 937,
    alpha: 945,
    beta: 946,
    gamma: 947,
    delta: 948,
    epsilon: 949,
    zeta: 950,
    eta: 951,
    theta: 952,
    iota: 953,
    kappa: 954,
    lambda: 955,
    mu: 956,
    nu: 957,
    xi: 958,
    omicron: 959,
    pi: 960,
    rho: 961,
    sigmaf: 962,
    sigma: 963,
    tau: 964,
    upsilon: 965,
    phi: 966,
    chi: 967,
    psi: 968,
    omega: 969,
    thetasym: 977,
    upsih: 978,
    piv: 982,
    bull: 8226,
    hellip: 8230,
    prime: 8242,
    Prime: 8243,
    oline: 8254,
    frasl: 8260,
    weierp: 8472,
    image: 8465,
    real: 8476,
    trade: 8482,
    alefsym: 8501,
    larr: 8592,
    uarr: 8593,
    rarr: 8594,
    darr: 8595,
    harr: 8596,
    crarr: 8629,
    lArr: 8656,
    uArr: 8657,
    rArr: 8658,
    dArr: 8659,
    hArr: 8660,
    forall: 8704,
    part: 8706,
    exist: 8707,
    empty: 8709,
    nabla: 8711,
    isin: 8712,
    notin: 8713,
    ni: 8715,
    prod: 8719,
    sum: 8721,
    minus: 8722,
    lowast: 8727,
    radic: 8730,
    prop: 8733,
    infin: 8734,
    ang: 8736,
    and: 8743,
    or: 8744,
    cap: 8745,
    cup: 8746,
    int: 8747,
    there4: 8756,
    sim: 8764,
    cong: 8773,
    asymp: 8776,
    ne: 8800,
    equiv: 8801,
    le: 8804,
    ge: 8805,
    sub: 8834,
    sup: 8835,
    nsub: 8836,
    sube: 8838,
    supe: 8839,
    oplus: 8853,
    otimes: 8855,
    perp: 8869,
    sdot: 8901,
    lceil: 8968,
    rceil: 8969,
    lfloor: 8970,
    rfloor: 8971,
    lang: 9001,
    rang: 9002,
    loz: 9674,
    spades: 9824,
    clubs: 9827,
    hearts: 9829,
    diams: 9830
  };
  var PathArguments = {
    A: 7,
    a: 7,
    C: 6,
    c: 6,
    H: 1,
    h: 1,
    L: 2,
    l: 2,
    M: 2,
    m: 2,
    Q: 4,
    q: 4,
    S: 4,
    s: 4,
    T: 2,
    t: 2,
    V: 1,
    v: 1,
    Z: 0,
    z: 0
  };
  var PathFlags = {
    A3: true,
    A4: true,
    a3: true,
    a4: true
  };
  var Properties = {
    'color': {
      inherit: true,
      initial: undefined
    },
    'visibility': {
      inherit: true,
      initial: 'visible',
      values: {
        'hidden': 'hidden',
        'collapse': 'hidden',
        'visible': 'visible'
      }
    },
    'fill': {
      inherit: true,
      initial: DefaultColors.black
    },
    'stroke': {
      inherit: true,
      initial: 'none'
    },
    'stop-color': {
      inherit: false,
      initial: DefaultColors.black
    },
    'fill-opacity': {
      inherit: true,
      initial: 1
    },
    'stroke-opacity': {
      inherit: true,
      initial: 1
    },
    'stop-opacity': {
      inherit: false,
      initial: 1
    },
    'fill-rule': {
      inherit: true,
      initial: 'nonzero',
      values: {
        'nonzero': 'nonzero',
        'evenodd': 'evenodd'
      }
    },
    'clip-rule': {
      inherit: true,
      initial: 'nonzero',
      values: {
        'nonzero': 'nonzero',
        'evenodd': 'evenodd'
      }
    },
    'stroke-width': {
      inherit: true,
      initial: 1
    },
    'stroke-dasharray': {
      inherit: true,
      initial: []
    },
    'stroke-dashoffset': {
      inherit: true,
      initial: 0
    },
    'stroke-miterlimit': {
      inherit: true,
      initial: 4
    },
    'stroke-linejoin': {
      inherit: true,
      initial: 'miter',
      values: {
        'miter': 'miter',
        'round': 'round',
        'bevel': 'bevel'
      }
    },
    'stroke-linecap': {
      inherit: true,
      initial: 'butt',
      values: {
        'butt': 'butt',
        'round': 'round',
        'square': 'square'
      }
    },
    'font-size': {
      inherit: true,
      initial: 16,
      values: {
        'xx-small': 9,
        'x-small': 10,
        'small': 13,
        'medium': 16,
        'large': 18,
        'x-large': 24,
        'xx-large': 32
      }
    },
    'font-family': {
      inherit: true,
      initial: 'sans-serif'
    },
    'font-weight': {
      inherit: true,
      initial: 'normal',
      values: {
        '600': 'bold',
        '700': 'bold',
        '800': 'bold',
        '900': 'bold',
        'bold': 'bold',
        'bolder': 'bold',
        '500': 'normal',
        '400': 'normal',
        '300': 'normal',
        '200': 'normal',
        '100': 'normal',
        'normal': 'normal',
        'lighter': 'normal'
      }
    },
    'font-style': {
      inherit: true,
      initial: 'normal',
      values: {
        'italic': 'italic',
        'oblique': 'italic',
        'normal': 'normal'
      }
    },
    'text-anchor': {
      inherit: true,
      initial: 'start',
      values: {
        'start': 'start',
        'middle': 'middle',
        'end': 'end'
      }
    },
    'direction': {
      inherit: true,
      initial: 'ltr',
      values: {
        'ltr': 'ltr',
        'rtl': 'rtl'
      }
    },
    'dominant-baseline': {
      inherit: true,
      initial: 'baseline',
      values: {
        'auto': 'baseline',
        'baseline': 'baseline',
        'before-edge': 'before-edge',
        'text-before-edge': 'before-edge',
        'middle': 'middle',
        'central': 'central',
        'after-edge': 'after-edge',
        'text-after-edge': 'after-edge',
        'ideographic': 'ideographic',
        'alphabetic': 'alphabetic',
        'hanging': 'hanging',
        'mathematical': 'mathematical'
      }
    },
    'alignment-baseline': {
      inherit: false,
      initial: undefined,
      values: {
        'auto': 'baseline',
        'baseline': 'baseline',
        'before-edge': 'before-edge',
        'text-before-edge': 'before-edge',
        'middle': 'middle',
        'central': 'central',
        'after-edge': 'after-edge',
        'text-after-edge': 'after-edge',
        'ideographic': 'ideographic',
        'alphabetic': 'alphabetic',
        'hanging': 'hanging',
        'mathematical': 'mathematical'
      }
    },
    'baseline-shift': {
      inherit: true,
      initial: 'baseline',
      values: {
        'baseline': 'baseline',
        'sub': 'sub',
        'super': 'super'
      }
    },
    'word-spacing': {
      inherit: true,
      initial: 0,
      values: {
        normal: 0
      }
    },
    'letter-spacing': {
      inherit: true,
      initial: 0,
      values: {
        normal: 0
      }
    },
    'text-decoration': {
      inherit: false,
      initial: 'none',
      values: {
        'none': 'none',
        'underline': 'underline',
        'overline': 'overline',
        'line-through': 'line-through'
      }
    },
    'xml:space': {
      inherit: true,
      initial: 'default',
      css: 'white-space',
      values: {
        'preserve': 'preserve',
        'default': 'default',
        'pre': 'preserve',
        'pre-line': 'preserve',
        'pre-wrap': 'preserve',
        'nowrap': 'default'
      }
    },
    'marker-start': {
      inherit: true,
      initial: 'none'
    },
    'marker-mid': {
      inherit: true,
      initial: 'none'
    },
    'marker-end': {
      inherit: true,
      initial: 'none'
    },
    'opacity': {
      inherit: false,
      initial: 1
    },
    'transform': {
      inherit: false,
      initial: [1, 0, 0, 1, 0, 0]
    },
    'display': {
      inherit: false,
      initial: 'inline',
      values: {
        'none': 'none',
        'inline': 'inline',
        'block': 'inline'
      }
    },
    'clip-path': {
      inherit: false,
      initial: 'none'
    },
    'mask': {
      inherit: false,
      initial: 'none'
    },
    'overflow': {
      inherit: false,
      initial: 'hidden',
      values: {
        'hidden': 'hidden',
        'scroll': 'hidden',
        'visible': 'visible'
      }
    }
  };
  function docBeginGroup(bbox) {
    var group = new function PDFGroup() {}();
    group.name = 'G' + (doc._groupCount = (doc._groupCount || 0) + 1);
    group.resources = doc.ref();
    group.xobj = doc.ref({
      Type: 'XObject',
      Subtype: 'Form',
      FormType: 1,
      BBox: bbox,
      Group: {
        S: 'Transparency',
        CS: 'DeviceRGB',
        I: true,
        K: false
      },
      Resources: group.resources
    });
    group.xobj.write('');
    group.savedMatrix = doc._ctm;
    group.savedPage = doc.page;
    groupStack.push(group);
    doc._ctm = [1, 0, 0, 1, 0, 0];
    doc.page = {
      width: doc.page.width,
      height: doc.page.height,
      write: function write(data) {
        group.xobj.write(data);
      },
      fonts: {},
      xobjects: {},
      ext_gstates: {},
      patterns: {}
    };
    return group;
  }
  function docEndGroup(group) {
    if (group !== groupStack.pop()) {
      throw 'Group not matching';
    }
    if (Object.keys(doc.page.fonts).length) {
      group.resources.data.Font = doc.page.fonts;
    }
    if (Object.keys(doc.page.xobjects).length) {
      group.resources.data.XObject = doc.page.xobjects;
    }
    if (Object.keys(doc.page.ext_gstates).length) {
      group.resources.data.ExtGState = doc.page.ext_gstates;
    }
    if (Object.keys(doc.page.patterns).length) {
      group.resources.data.Pattern = doc.page.patterns;
    }
    group.resources.end();
    group.xobj.end();
    doc._ctm = group.savedMatrix;
    doc.page = group.savedPage;
  }
  function docInsertGroup(group) {
    doc.page.xobjects[group.name] = group.xobj;
    doc.addContent('/' + group.name + ' Do');
  }
  function docApplyMask(group, clip) {
    var name = 'M' + (doc._maskCount = (doc._maskCount || 0) + 1);
    var gstate = doc.ref({
      Type: 'ExtGState',
      CA: 1,
      ca: 1,
      BM: 'Normal',
      SMask: {
        S: 'Luminosity',
        G: group.xobj,
        BC: clip ? [0, 0, 0] : [1, 1, 1]
      }
    });
    gstate.end();
    doc.page.ext_gstates[name] = gstate;
    doc.addContent('/' + name + ' gs');
  }
  function docCreatePattern(group, dx, dy, matrix) {
    var pattern = new function PDFPattern() {}();
    pattern.group = group;
    pattern.dx = dx;
    pattern.dy = dy;
    pattern.matrix = matrix || [1, 0, 0, 1, 0, 0];
    return pattern;
  }
  function docUsePattern(pattern, stroke) {
    var name = 'P' + (doc._patternCount = (doc._patternCount || 0) + 1);
    var ref = doc.ref({
      Type: 'Pattern',
      PatternType: 1,
      PaintType: 1,
      TilingType: 2,
      BBox: [0, 0, pattern.dx, pattern.dy],
      XStep: pattern.dx,
      YStep: pattern.dy,
      Matrix: multiplyMatrix(doc._ctm, pattern.matrix),
      Resources: {
        ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
        XObject: function () {
          var temp = {};
          temp[pattern.group.name] = pattern.group.xobj;
          return temp;
        }()
      }
    });
    ref.write('/' + pattern.group.name + ' Do');
    ref.end();
    doc.page.patterns[name] = ref;
    if (stroke) {
      doc.addContent('/Pattern CS');
      doc.addContent('/' + name + ' SCN');
    } else {
      doc.addContent('/Pattern cs');
      doc.addContent('/' + name + ' scn');
    }
  }
  function docBeginText(font, size) {
    if (!doc.page.fonts[font.id]) {
      doc.page.fonts[font.id] = font.ref();
    }
    doc.addContent('BT').addContent('/' + font.id + ' ' + size + ' Tf');
  }
  function docSetTextMatrix(a, b, c, d, e, f) {
    doc.addContent(validateNumber(a) + ' ' + validateNumber(b) + ' ' + validateNumber(-c) + ' ' + validateNumber(-d) + ' ' + validateNumber(e) + ' ' + validateNumber(f) + ' Tm');
  }
  function docSetTextMode(fill, stroke) {
    var mode = fill && stroke ? 2 : stroke ? 1 : fill ? 0 : 3;
    doc.addContent(mode + ' Tr');
  }
  function docWriteGlyph(glyph) {
    doc.addContent('<' + glyph + '> Tj');
  }
  function docEndText() {
    doc.addContent('ET');
  }
  function docFillColor(color) {
    if (color[0].constructor.name === 'PDFPattern') {
      doc.fillOpacity(color[1]);
      docUsePattern(color[0], false);
    } else {
      doc.fillColor(color[0], color[1]);
    }
  }
  function docStrokeColor(color) {
    if (color[0].constructor.name === 'PDFPattern') {
      doc.strokeOpacity(color[1]);
      docUsePattern(color[0], true);
    } else {
      doc.strokeColor(color[0], color[1]);
    }
  }
  function docInsertLink(x, y, w, h, url) {
    var ref = doc.ref({
      Type: 'Annot',
      Subtype: 'Link',
      Rect: [x, y, w, h],
      Border: [0, 0, 0],
      A: {
        S: 'URI',
        URI: new String(url)
      }
    });
    ref.end();
    links.push(ref);
  }
  function parseXml(xml) {
    var SvgNode = function SvgNode(tag, type, value, error) {
      this.error = error;
      this.nodeName = tag;
      this.nodeValue = value;
      this.nodeType = type;
      this.attributes = Object.create(null);
      this.childNodes = [];
      this.parentNode = null;
      this.id = '';
      this.textContent = '';
      this.classList = [];
    };
    SvgNode.prototype.getAttribute = function (attr) {
      return this.attributes[attr] != null ? this.attributes[attr] : null;
    };
    SvgNode.prototype.getElementById = function (id) {
      var result = null;
      (function recursive(node) {
        if (result) {
          return;
        }
        if (node.nodeType === 1) {
          if (node.id === id) {
            result = node;
          }
          for (var i = 0; i < node.childNodes.length; i++) {
            recursive(node.childNodes[i]);
          }
        }
      })(this);
      return result;
    };
    SvgNode.prototype.getElementsByTagName = function (tag) {
      var result = [];
      (function recursive(node) {
        if (node.nodeType === 1) {
          if (node.nodeName === tag) {
            result.push(node);
          }
          for (var i = 0; i < node.childNodes.length; i++) {
            recursive(node.childNodes[i]);
          }
        }
      })(this);
      return result;
    };
    var parser = new StringParser(xml.trim()),
      result,
      child,
      error = false;
    var _recursive = function recursive() {
      var temp, child;
      if (temp = parser.match(/^<([\w:.-]+)\s*/, true)) {
        // Opening tag
        var node = new SvgNode(temp[1], 1, null, error);
        while (temp = parser.match(/^([\w:.-]+)(?:\s*=\s*"([^"]*)"|\s*=\s*'([^']*)')?\s*/, true)) {
          // Attribute
          var attr = temp[1],
            value = decodeEntities(temp[2] || temp[3] || '');
          if (!node.attributes[attr]) {
            node.attributes[attr] = value;
            if (attr === 'id') {
              node.id = value;
            }
            if (attr === 'class') {
              node.classList = value.split(' ');
            }
          } else {
            warningCallback('parseXml: duplicate attribute "' + attr + '"');
            error = true;
          }
        }
        if (parser.match(/^>/)) {
          // End of opening tag
          while (child = _recursive()) {
            node.childNodes.push(child);
            child.parentNode = node;
            node.textContent += child.nodeType === 3 || child.nodeType === 4 ? child.nodeValue : child.textContent;
          }
          if (temp = parser.match(/^<\/([\w:.-]+)\s*>/, true)) {
            // Closing tag
            if (temp[1] === node.nodeName) {
              return node;
            } else {
              warningCallback('parseXml: tag not matching, opening "' + node.nodeName + '" & closing "' + temp[1] + '"');
              error = true;
              return node;
            }
          } else {
            warningCallback('parseXml: tag not matching, opening "' + node.nodeName + '" & not closing');
            error = true;
            return node;
          }
        } else if (parser.match(/^\/>/)) {
          // Self-closing tag
          return node;
        } else {
          warningCallback('parseXml: tag could not be parsed "' + node.nodeName + '"');
          error = true;
        }
      } else if (temp = parser.match(/^<!--[\s\S]*?-->/)) {
        // Comment
        return new SvgNode(null, 8, temp, error);
      } else if (temp = parser.match(/^<\?[\s\S]*?\?>/)) {
        // Processing instructions
        return new SvgNode(null, 7, temp, error);
      } else if (temp = parser.match(/^<!DOCTYPE\s*([\s\S]*?)>/)) {
        // Doctype
        return new SvgNode(null, 10, temp, error);
      } else if (temp = parser.match(/^<!\[CDATA\[([\s\S]*?)\]\]>/, true)) {
        // Cdata node
        return new SvgNode('#cdata-section', 4, temp[1], error);
      } else if (temp = parser.match(/^([^<]+)/, true)) {
        // Text node
        return new SvgNode('#text', 3, decodeEntities(temp[1]), error);
      }
    };
    while (child = _recursive()) {
      if (child.nodeType === 1 && !result) {
        result = child;
      } else if (child.nodeType === 1 || child.nodeType === 3 && child.nodeValue.trim() !== '') {
        warningCallback('parseXml: data after document end has been discarded');
      }
    }
    if (parser.matchAll()) {
      warningCallback('parseXml: parsing error');
    }
    return result;
  }
  ;
  function decodeEntities(str) {
    return str.replace(/&(?:#([0-9]+)|#[xX]([0-9A-Fa-f]+)|([0-9A-Za-z]+));/g, function (mt, m0, m1, m2) {
      if (m0) {
        return String.fromCharCode(parseInt(m0, 10));
      } else if (m1) {
        return String.fromCharCode(parseInt(m1, 16));
      } else if (m2 && Entities[m2]) {
        return String.fromCharCode(Entities[m2]);
      } else {
        return mt;
      }
    });
  }
  function parseColor(raw) {
    var temp, result;
    raw = (raw || '').trim();
    if (temp = NamedColors[raw]) {
      result = [temp.slice(), 1];
    } else if (temp = raw.match(/^rgba\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9.]+)\s*\)$/i)) {
      temp[1] = parseInt(temp[1]);
      temp[2] = parseInt(temp[2]);
      temp[3] = parseInt(temp[3]);
      temp[4] = parseFloat(temp[4]);
      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256 && temp[4] <= 1) {
        result = [temp.slice(1, 4), temp[4]];
      }
    } else if (temp = raw.match(/^rgb\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)\s*\)$/i)) {
      temp[1] = parseInt(temp[1]);
      temp[2] = parseInt(temp[2]);
      temp[3] = parseInt(temp[3]);
      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256) {
        result = [temp.slice(1, 4), 1];
      }
    } else if (temp = raw.match(/^rgb\(\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*,\s*([0-9.]+)%\s*\)$/i)) {
      temp[1] = 2.55 * parseFloat(temp[1]);
      temp[2] = 2.55 * parseFloat(temp[2]);
      temp[3] = 2.55 * parseFloat(temp[3]);
      if (temp[1] < 256 && temp[2] < 256 && temp[3] < 256) {
        result = [temp.slice(1, 4), 1];
      }
    } else if (temp = raw.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)) {
      result = [[parseInt(temp[1], 16), parseInt(temp[2], 16), parseInt(temp[3], 16)], 1];
    } else if (temp = raw.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i)) {
      result = [[0x11 * parseInt(temp[1], 16), 0x11 * parseInt(temp[2], 16), 0x11 * parseInt(temp[3], 16)], 1];
    }
    return colorCallback ? colorCallback(result, raw) : result;
  }
  function opacityToColor(color, opacity, isMask) {
    var newColor = color[0].slice(),
      newOpacity = color[1] * opacity;
    if (isMask) {
      for (var i = 0; i < color.length; i++) {
        newColor[i] *= newOpacity;
      }
      return [newColor, 1];
    } else {
      return [newColor, newOpacity];
    }
  }
  function multiplyMatrix() {
    function multiply(a, b) {
      return [a[0] * b[0] + a[2] * b[1], a[1] * b[0] + a[3] * b[1], a[0] * b[2] + a[2] * b[3], a[1] * b[2] + a[3] * b[3], a[0] * b[4] + a[2] * b[5] + a[4], a[1] * b[4] + a[3] * b[5] + a[5]];
    }
    var result = arguments[0];
    for (var i = 1; i < arguments.length; i++) {
      result = multiply(result, arguments[i]);
    }
    return result;
  }
  function transformPoint(p, m) {
    return [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]];
  }
  function getGlobalMatrix() {
    var ctm = doc._ctm;
    for (var i = groupStack.length - 1; i >= 0; i--) {
      ctm = multiplyMatrix(groupStack[i].savedMatrix, ctm);
    }
    return ctm;
  }
  function getPageBBox() {
    return new _SvgShape().M(0, 0).L(doc.page.width, 0).L(doc.page.width, doc.page.height).L(0, doc.page.height).transform(inverseMatrix(getGlobalMatrix())).getBoundingBox();
  }
  function inverseMatrix(m) {
    var dt = m[0] * m[3] - m[1] * m[2];
    return [m[3] / dt, -m[1] / dt, -m[2] / dt, m[0] / dt, (m[2] * m[5] - m[3] * m[4]) / dt, (m[1] * m[4] - m[0] * m[5]) / dt];
  }
  function validateMatrix(m) {
    var m0 = validateNumber(m[0]),
      m1 = validateNumber(m[1]),
      m2 = validateNumber(m[2]),
      m3 = validateNumber(m[3]),
      m4 = validateNumber(m[4]),
      m5 = validateNumber(m[5]);
    if (isNotEqual(m0 * m3 - m1 * m2, 0)) {
      return [m0, m1, m2, m3, m4, m5];
    }
  }
  function solveEquation(curve) {
    var a = curve[2] || 0,
      b = curve[1] || 0,
      c = curve[0] || 0;
    if (isEqual(a, 0) && isEqual(b, 0)) {
      return [];
    } else if (isEqual(a, 0)) {
      return [-c / b];
    } else {
      var d = b * b - 4 * a * c;
      if (isNotEqual(d, 0) && d > 0) {
        return [(-b + Math.sqrt(d)) / (2 * a), (-b - Math.sqrt(d)) / (2 * a)];
      } else if (isEqual(d, 0)) {
        return [-b / (2 * a)];
      } else {
        return [];
      }
    }
  }
  function getCurveValue(t, curve) {
    return (curve[0] || 0) + (curve[1] || 0) * t + (curve[2] || 0) * t * t + (curve[3] || 0) * t * t * t;
  }
  function isEqual(number, ref) {
    return Math.abs(number - ref) < 1e-10;
  }
  function isNotEqual(number, ref) {
    return Math.abs(number - ref) >= 1e-10;
  }
  function validateNumber(n) {
    return n > -1e21 && n < 1e21 ? Math.round(n * 1e6) / 1e6 : 0;
  }
  function isArrayLike(v) {
    return typeof v === 'object' && v !== null && typeof v.length === 'number';
  }
  function parseTranform(v) {
    var parser = new StringParser((v || '').trim()),
      result = [1, 0, 0, 1, 0, 0],
      temp;
    while (temp = parser.match(/^([A-Za-z]+)\s*[(]([^(]+)[)]/, true)) {
      var func = temp[1],
        nums = [],
        parser2 = new StringParser(temp[2].trim()),
        temp2 = void 0;
      while (temp2 = parser2.matchNumber()) {
        nums.push(Number(temp2));
        parser2.matchSeparator();
      }
      if (func === 'matrix' && nums.length === 6) {
        result = multiplyMatrix(result, [nums[0], nums[1], nums[2], nums[3], nums[4], nums[5]]);
      } else if (func === 'translate' && nums.length === 2) {
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[0], nums[1]]);
      } else if (func === 'translate' && nums.length === 1) {
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[0], 0]);
      } else if (func === 'scale' && nums.length === 2) {
        result = multiplyMatrix(result, [nums[0], 0, 0, nums[1], 0, 0]);
      } else if (func === 'scale' && nums.length === 1) {
        result = multiplyMatrix(result, [nums[0], 0, 0, nums[0], 0, 0]);
      } else if (func === 'rotate' && nums.length === 3) {
        var a = nums[0] * Math.PI / 180;
        result = multiplyMatrix(result, [1, 0, 0, 1, nums[1], nums[2]], [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a), 0, 0], [1, 0, 0, 1, -nums[1], -nums[2]]);
      } else if (func === 'rotate' && nums.length === 1) {
        var _a = nums[0] * Math.PI / 180;
        result = multiplyMatrix(result, [Math.cos(_a), Math.sin(_a), -Math.sin(_a), Math.cos(_a), 0, 0]);
      } else if (func === 'skewX' && nums.length === 1) {
        var _a2 = nums[0] * Math.PI / 180;
        result = multiplyMatrix(result, [1, 0, Math.tan(_a2), 1, 0, 0]);
      } else if (func === 'skewY' && nums.length === 1) {
        var _a3 = nums[0] * Math.PI / 180;
        result = multiplyMatrix(result, [1, Math.tan(_a3), 0, 1, 0, 0]);
      } else {
        return;
      }
      parser.matchSeparator();
    }
    if (parser.matchAll()) {
      return;
    }
    return result;
  }
  function parseAspectRatio(aspectRatio, availWidth, availHeight, elemWidth, elemHeight, initAlign) {
    var temp = (aspectRatio || '').trim().match(/^(none)$|^x(Min|Mid|Max)Y(Min|Mid|Max)(?:\s+(meet|slice))?$/) || [],
      ratioType = temp[1] || temp[4] || 'meet',
      xAlign = temp[2] || 'Mid',
      yAlign = temp[3] || 'Mid',
      scaleX = availWidth / elemWidth,
      scaleY = availHeight / elemHeight,
      dx = {
        'Min': 0,
        'Mid': 0.5,
        'Max': 1
      }[xAlign] - (initAlign || 0),
      dy = {
        'Min': 0,
        'Mid': 0.5,
        'Max': 1
      }[yAlign] - (initAlign || 0);
    if (ratioType === 'slice') {
      scaleY = scaleX = Math.max(scaleX, scaleY);
    } else if (ratioType === 'meet') {
      scaleY = scaleX = Math.min(scaleX, scaleY);
    }
    return [scaleX, 0, 0, scaleY, dx * (availWidth - elemWidth * scaleX), dy * (availHeight - elemHeight * scaleY)];
  }
  function parseStyleAttr(v) {
    var result = Object.create(null);
    v = (v || '').trim().split(/;/);
    for (var i = 0; i < v.length; i++) {
      var key = (v[i].split(':')[0] || '').trim(),
        value = (v[i].split(':')[1] || '').trim();
      if (key) {
        result[key] = value;
      }
    }
    if (result['marker']) {
      if (!result['marker-start']) {
        result['marker-start'] = result['marker'];
      }
      if (!result['marker-mid']) {
        result['marker-mid'] = result['marker'];
      }
      if (!result['marker-end']) {
        result['marker-end'] = result['marker'];
      }
    }
    if (result['font']) {
      var fontFamily = null,
        fontSize = null,
        fontStyle = "normal",
        fontWeight = "normal",
        fontVariant = "normal";
      var parts = result['font'].split(/\s+/);
      for (var _i = 0; _i < parts.length; _i++) {
        switch (parts[_i]) {
          case "normal":
            break;
          case "italic":
          case "oblique":
            fontStyle = parts[_i];
            break;
          case "small-caps":
            fontVariant = parts[_i];
            break;
          case "bold":
          case "bolder":
          case "lighter":
          case "100":
          case "200":
          case "300":
          case "400":
          case "500":
          case "600":
          case "700":
          case "800":
          case "900":
            fontWeight = parts[_i];
            break;
          default:
            if (!fontSize) {
              fontSize = parts[_i].split('/')[0];
            } else {
              if (!fontFamily) {
                fontFamily = parts[_i];
              } else {
                fontFamily += ' ' + parts[_i];
              }
            }
            break;
        }
      }
      if (!result['font-style']) {
        result['font-style'] = fontStyle;
      }
      if (!result['font-variant']) {
        result['font-variant'] = fontVariant;
      }
      if (!result['font-weight']) {
        result['font-weight'] = fontWeight;
      }
      if (!result['font-size']) {
        result['font-size'] = fontSize;
      }
      if (!result['font-family']) {
        result['font-family'] = fontFamily;
      }
    }
    return result;
  }
  function parseSelector(v) {
    var parts = v.split(/(?=[.#])/g),
      ids = [],
      classes = [],
      tags = [],
      temp;
    for (var i = 0; i < parts.length; i++) {
      if (temp = parts[i].match(/^[#]([_A-Za-z0-9-]+)$/)) {
        ids.push(temp[1]);
      } else if (temp = parts[i].match(/^[.]([_A-Za-z0-9-]+)$/)) {
        classes.push(temp[1]);
      } else if (temp = parts[i].match(/^([_A-Za-z0-9-]+)$/)) {
        tags.push(temp[1]);
      } else if (parts[i] !== '*') {
        return;
      }
    }
    return {
      tags: tags,
      ids: ids,
      classes: classes,
      specificity: ids.length * 10000 + classes.length * 100 + tags.length
    };
  }
  function parseStyleSheet(v) {
    var parser = new StringParser(v.trim()),
      rules = [],
      rule;
    while (rule = parser.match(/^\s*([^\{\}]*?)\s*\{([^\{\}]*?)\}/, true)) {
      var selectors = rule[1].split(/\s*,\s*/g),
        css = parseStyleAttr(rule[2]);
      for (var i = 0; i < selectors.length; i++) {
        var selector = parseSelector(selectors[i]);
        if (selector) {
          rules.push({
            selector: selector,
            css: css
          });
        }
      }
    }
    return rules;
  }
  function matchesSelector(elem, selector) {
    if (elem.nodeType !== 1) {
      return false;
    }
    for (var i = 0; i < selector.tags.length; i++) {
      if (selector.tags[i] !== elem.nodeName) {
        return false;
      }
    }
    for (var _i2 = 0; _i2 < selector.ids.length; _i2++) {
      if (selector.ids[_i2] !== elem.id) {
        return false;
      }
    }
    for (var _i3 = 0; _i3 < selector.classes.length; _i3++) {
      if (elem.classList.indexOf(selector.classes[_i3]) === -1) {
        return false;
      }
    }
    return true;
  }
  function getStyle(elem) {
    var result = Object.create(null);
    var specificities = Object.create(null);
    for (var i = 0; i < styleRules.length; i++) {
      var rule = styleRules[i];
      if (matchesSelector(elem, rule.selector)) {
        for (var key in rule.css) {
          if (!(specificities[key] > rule.selector.specificity)) {
            result[key] = rule.css[key];
            specificities[key] = rule.selector.specificity;
          }
        }
      }
    }
    return result;
  }
  function combineArrays(array1, array2) {
    return array1.concat(array2.slice(array1.length));
  }
  function getAscent(font, size) {
    return Math.max(font.ascender, (font.bbox[3] || font.bbox.maxY) * (font.scale || 1)) * size / 1000;
  }
  function getDescent(font, size) {
    return Math.min(font.descender, (font.bbox[1] || font.bbox.minY) * (font.scale || 1)) * size / 1000;
  }
  function getXHeight(font, size) {
    return (font.xHeight || 0.5 * (font.ascender - font.descender)) * size / 1000;
  }
  function getBaseline(font, size, baseline, shift) {
    var dy1, dy2;
    switch (baseline) {
      case 'middle':
        dy1 = 0.5 * getXHeight(font, size);
        break;
      case 'central':
        dy1 = 0.5 * (getDescent(font, size) + getAscent(font, size));
        break;
      case 'after-edge':
      case 'text-after-edge':
        dy1 = getDescent(font, size);
        break;
      case 'alphabetic':
      case 'auto':
      case 'baseline':
        dy1 = 0;
        break;
      case 'mathematical':
        dy1 = 0.5 * getAscent(font, size);
        break;
      case 'hanging':
        dy1 = 0.8 * getAscent(font, size);
        break;
      case 'before-edge':
      case 'text-before-edge':
        dy1 = getAscent(font, size);
        break;
      default:
        dy1 = 0;
        break;
    }
    switch (shift) {
      case 'baseline':
        dy2 = 0;
        break;
      case 'super':
        dy2 = 0.6 * size;
        break;
      case 'sub':
        dy2 = -0.6 * size;
        break;
      default:
        dy2 = shift;
        break;
    }
    return dy1 - dy2;
  }
  function getTextPos(font, size, text) {
    var encoded = font.encode('' + text),
      hex = encoded[0],
      pos = encoded[1],
      data = [];
    for (var i = 0; i < hex.length; i++) {
      var unicode = font.unicode ? font.unicode[parseInt(hex[i], 16)] : [text.charCodeAt(i)];
      data.push({
        glyph: hex[i],
        unicode: unicode,
        width: pos[i].advanceWidth * size / 1000,
        xOffset: pos[i].xOffset * size / 1000,
        yOffset: pos[i].yOffset * size / 1000,
        xAdvance: pos[i].xAdvance * size / 1000,
        yAdvance: pos[i].yAdvance * size / 1000
      });
    }
    return data;
  }
  function createSVGElement(obj, inherits) {
    switch (obj.nodeName) {
      case 'use':
        return new SvgElemUse(obj, inherits);
      case 'symbol':
        return new SvgElemSymbol(obj, inherits);
      case 'g':
        return new SvgElemGroup(obj, inherits);
      case 'a':
        return new SvgElemLink(obj, inherits);
      case 'svg':
        return new SvgElemSvg(obj, inherits);
      case 'image':
        return new SVGElemImage(obj, inherits);
      case 'rect':
        return new SvgElemRect(obj, inherits);
      case 'circle':
        return new SvgElemCircle(obj, inherits);
      case 'ellipse':
        return new SvgElemEllipse(obj, inherits);
      case 'line':
        return new SvgElemLine(obj, inherits);
      case 'polyline':
        return new SvgElemPolyline(obj, inherits);
      case 'polygon':
        return new SvgElemPolygon(obj, inherits);
      case 'path':
        return new SvgElemPath(obj, inherits);
      case 'text':
        return new SvgElemText(obj, inherits);
      case 'tspan':
        return new SvgElemTspan(obj, inherits);
      case 'textPath':
        return new SvgElemTextPath(obj, inherits);
      case '#text':
      case '#cdata-section':
        return new SvgElemTextNode(obj, inherits);
      default:
        return new SvgElem(obj, inherits);
    }
  }
  var StringParser = function StringParser(str) {
    this.match = function (exp, all) {
      var temp = str.match(exp);
      if (!temp || temp.index !== 0) {
        return;
      }
      str = str.substring(temp[0].length);
      return all ? temp : temp[0];
    };
    this.matchSeparator = function () {
      return this.match(/^(?:\s*,\s*|\s*|)/);
    };
    this.matchSpace = function () {
      return this.match(/^(?:\s*)/);
    };
    this.matchLengthUnit = function () {
      return this.match(/^(?:px|pt|cm|mm|in|pc|em|ex|%|)/);
    };
    this.matchNumber = function () {
      return this.match(/^(?:[-+]?(?:[0-9]+[.][0-9]+|[0-9]+[.]|[.][0-9]+|[0-9]+)(?:[eE][-+]?[0-9]+)?)/);
    };
    this.matchAll = function () {
      return this.match(/^[\s\S]+/);
    };
  };
  var BezierSegment = function BezierSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y) {
    var divisions = 6 * precision;
    var equationX = [p1x, -3 * p1x + 3 * c1x, 3 * p1x - 6 * c1x + 3 * c2x, -p1x + 3 * c1x - 3 * c2x + p2x];
    var equationY = [p1y, -3 * p1y + 3 * c1y, 3 * p1y - 6 * c1y + 3 * c2y, -p1y + 3 * c1y - 3 * c2y + p2y];
    var derivativeX = [-3 * p1x + 3 * c1x, 6 * p1x - 12 * c1x + 6 * c2x, -3 * p1x + 9 * c1x - 9 * c2x + 3 * p2x];
    var derivativeY = [-3 * p1y + 3 * c1y, 6 * p1y - 12 * c1y + 6 * c2y, -3 * p1y + 9 * c1y - 9 * c2y + 3 * p2y];
    var lengthMap = [0];
    for (var i = 1; i <= divisions; i++) {
      var t = (i - 0.5) / divisions;
      var dx = getCurveValue(t, derivativeX) / divisions,
        dy = getCurveValue(t, derivativeY) / divisions,
        l = Math.sqrt(dx * dx + dy * dy);
      lengthMap[i] = lengthMap[i - 1] + l;
    }
    this.totalLength = lengthMap[divisions];
    this.startPoint = [p1x, p1y, isEqual(p1x, c1x) && isEqual(p1y, c1y) ? Math.atan2(c2y - c1y, c2x - c1x) : Math.atan2(c1y - p1y, c1x - p1x)];
    this.endPoint = [p2x, p2y, isEqual(c2x, p2x) && isEqual(c2y, p2y) ? Math.atan2(c2y - c1y, c2x - c1x) : Math.atan2(p2y - c2y, p2x - c2x)];
    this.getBoundingBox = function () {
      var temp;
      var minX = getCurveValue(0, equationX),
        minY = getCurveValue(0, equationY),
        maxX = getCurveValue(1, equationX),
        maxY = getCurveValue(1, equationY);
      if (minX > maxX) {
        temp = maxX;
        maxX = minX;
        minX = temp;
      }
      if (minY > maxY) {
        temp = maxY;
        maxY = minY;
        minY = temp;
      }
      var rootsX = solveEquation(derivativeX);
      for (var _i4 = 0; _i4 < rootsX.length; _i4++) {
        if (rootsX[_i4] >= 0 && rootsX[_i4] <= 1) {
          var _x = getCurveValue(rootsX[_i4], equationX);
          if (_x < minX) {
            minX = _x;
          }
          if (_x > maxX) {
            maxX = _x;
          }
        }
      }
      var rootsY = solveEquation(derivativeY);
      for (var _i5 = 0; _i5 < rootsY.length; _i5++) {
        if (rootsY[_i5] >= 0 && rootsY[_i5] <= 1) {
          var _y = getCurveValue(rootsY[_i5], equationY);
          if (_y < minY) {
            minY = _y;
          }
          if (_y > maxY) {
            maxY = _y;
          }
        }
      }
      return [minX, minY, maxX, maxY];
    };
    this.getPointAtLength = function (l) {
      if (isEqual(l, 0)) {
        return this.startPoint;
      }
      if (isEqual(l, this.totalLength)) {
        return this.endPoint;
      }
      if (l < 0 || l > this.totalLength) {
        return;
      }
      for (var _i6 = 1; _i6 <= divisions; _i6++) {
        var l1 = lengthMap[_i6 - 1],
          l2 = lengthMap[_i6];
        if (l1 <= l && l <= l2) {
          var _t = (_i6 - (l2 - l) / (l2 - l1)) / divisions,
            _x2 = getCurveValue(_t, equationX),
            _y2 = getCurveValue(_t, equationY),
            _dx = getCurveValue(_t, derivativeX),
            _dy = getCurveValue(_t, derivativeY);
          return [_x2, _y2, Math.atan2(_dy, _dx)];
        }
      }
    };
  };
  var LineSegment = function LineSegment(p1x, p1y, p2x, p2y) {
    this.totalLength = Math.sqrt((p2x - p1x) * (p2x - p1x) + (p2y - p1y) * (p2y - p1y));
    this.startPoint = [p1x, p1y, Math.atan2(p2y - p1y, p2x - p1x)];
    this.endPoint = [p2x, p2y, Math.atan2(p2y - p1y, p2x - p1x)];
    this.getBoundingBox = function () {
      return [Math.min(this.startPoint[0], this.endPoint[0]), Math.min(this.startPoint[1], this.endPoint[1]), Math.max(this.startPoint[0], this.endPoint[0]), Math.max(this.startPoint[1], this.endPoint[1])];
    };
    this.getPointAtLength = function (l) {
      if (l >= 0 && l <= this.totalLength) {
        var r = l / this.totalLength || 0,
          _x3 = this.startPoint[0] + r * (this.endPoint[0] - this.startPoint[0]),
          _y3 = this.startPoint[1] + r * (this.endPoint[1] - this.startPoint[1]);
        return [_x3, _y3, this.startPoint[2]];
      }
    };
  };
  var _SvgShape = function SvgShape() {
    this.pathCommands = [];
    this.pathSegments = [];
    this.startPoint = null;
    this.endPoint = null;
    this.totalLength = 0;
    var startX = 0,
      startY = 0,
      currX = 0,
      currY = 0,
      lastCom,
      lastCtrlX,
      lastCtrlY;
    this.move = function (x, y) {
      startX = currX = x;
      startY = currY = y;
      return null;
    };
    this.line = function (x, y) {
      var segment = new LineSegment(currX, currY, x, y);
      currX = x;
      currY = y;
      return segment;
    };
    this.curve = function (c1x, c1y, c2x, c2y, x, y) {
      var segment = new BezierSegment(currX, currY, c1x, c1y, c2x, c2y, x, y);
      currX = x;
      currY = y;
      return segment;
    };
    this.close = function () {
      var segment = new LineSegment(currX, currY, startX, startY);
      currX = startX;
      currY = startY;
      return segment;
    };
    this.addCommand = function (data) {
      this.pathCommands.push(data);
      var segment = this[data[0]].apply(this, data.slice(3));
      if (segment) {
        segment.hasStart = data[1];
        segment.hasEnd = data[2];
        this.startPoint = this.startPoint || segment.startPoint;
        this.endPoint = segment.endPoint;
        this.pathSegments.push(segment);
        this.totalLength += segment.totalLength;
      }
    };
    this.M = function (x, y) {
      this.addCommand(['move', true, true, x, y]);
      lastCom = 'M';
      return this;
    };
    this.m = function (x, y) {
      return this.M(currX + x, currY + y);
    };
    this.Z = this.z = function () {
      this.addCommand(['close', true, true]);
      lastCom = 'Z';
      return this;
    };
    this.L = function (x, y) {
      this.addCommand(['line', true, true, x, y]);
      lastCom = 'L';
      return this;
    };
    this.l = function (x, y) {
      return this.L(currX + x, currY + y);
    };
    this.H = function (x) {
      return this.L(x, currY);
    };
    this.h = function (x) {
      return this.L(currX + x, currY);
    };
    this.V = function (y) {
      return this.L(currX, y);
    };
    this.v = function (y) {
      return this.L(currX, currY + y);
    };
    this.C = function (c1x, c1y, c2x, c2y, x, y) {
      this.addCommand(['curve', true, true, c1x, c1y, c2x, c2y, x, y]);
      lastCom = 'C';
      lastCtrlX = c2x;
      lastCtrlY = c2y;
      return this;
    };
    this.c = function (c1x, c1y, c2x, c2y, x, y) {
      return this.C(currX + c1x, currY + c1y, currX + c2x, currY + c2y, currX + x, currY + y);
    };
    this.S = function (c1x, c1y, x, y) {
      return this.C(currX + (lastCom === 'C' ? currX - lastCtrlX : 0), currY + (lastCom === 'C' ? currY - lastCtrlY : 0), c1x, c1y, x, y);
    };
    this.s = function (c1x, c1y, x, y) {
      return this.C(currX + (lastCom === 'C' ? currX - lastCtrlX : 0), currY + (lastCom === 'C' ? currY - lastCtrlY : 0), currX + c1x, currY + c1y, currX + x, currY + y);
    };
    this.Q = function (cx, cy, x, y) {
      var c1x = currX + 2 / 3 * (cx - currX),
        c1y = currY + 2 / 3 * (cy - currY),
        c2x = x + 2 / 3 * (cx - x),
        c2y = y + 2 / 3 * (cy - y);
      this.addCommand(['curve', true, true, c1x, c1y, c2x, c2y, x, y]);
      lastCom = 'Q';
      lastCtrlX = cx;
      lastCtrlY = cy;
      return this;
    };
    this.q = function (c1x, c1y, x, y) {
      return this.Q(currX + c1x, currY + c1y, currX + x, currY + y);
    };
    this.T = function (x, y) {
      return this.Q(currX + (lastCom === 'Q' ? currX - lastCtrlX : 0), currY + (lastCom === 'Q' ? currY - lastCtrlY : 0), x, y);
    };
    this.t = function (x, y) {
      return this.Q(currX + (lastCom === 'Q' ? currX - lastCtrlX : 0), currY + (lastCom === 'Q' ? currY - lastCtrlY : 0), currX + x, currY + y);
    };
    this.A = function (rx, ry, fi, fa, fs, x, y) {
      if (isEqual(rx, 0) || isEqual(ry, 0)) {
        this.addCommand(['line', true, true, x, y]);
      } else {
        fi = fi * (Math.PI / 180);
        rx = Math.abs(rx);
        ry = Math.abs(ry);
        fa = 1 * !!fa;
        fs = 1 * !!fs;
        var x1 = Math.cos(fi) * (currX - x) / 2 + Math.sin(fi) * (currY - y) / 2,
          y1 = Math.cos(fi) * (currY - y) / 2 - Math.sin(fi) * (currX - x) / 2,
          lambda = x1 * x1 / (rx * rx) + y1 * y1 / (ry * ry);
        if (lambda > 1) {
          rx *= Math.sqrt(lambda);
          ry *= Math.sqrt(lambda);
        }
        var r = Math.sqrt(Math.max(0, rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1) / (rx * rx * y1 * y1 + ry * ry * x1 * x1)),
          x2 = (fa === fs ? -1 : 1) * r * rx * y1 / ry,
          y2 = (fa === fs ? 1 : -1) * r * ry * x1 / rx;
        var cx = Math.cos(fi) * x2 - Math.sin(fi) * y2 + (currX + x) / 2,
          cy = Math.sin(fi) * x2 + Math.cos(fi) * y2 + (currY + y) / 2,
          th1 = Math.atan2((y1 - y2) / ry, (x1 - x2) / rx),
          th2 = Math.atan2((-y1 - y2) / ry, (-x1 - x2) / rx);
        if (fs === 0 && th2 - th1 > 0) {
          th2 -= 2 * Math.PI;
        } else if (fs === 1 && th2 - th1 < 0) {
          th2 += 2 * Math.PI;
        }
        var segms = Math.ceil(Math.abs(th2 - th1) / (Math.PI / precision));
        for (var i = 0; i < segms; i++) {
          var th3 = th1 + i * (th2 - th1) / segms,
            th4 = th1 + (i + 1) * (th2 - th1) / segms,
            t = 4 / 3 * Math.tan((th4 - th3) / 4);
          var c1x = cx + Math.cos(fi) * rx * (Math.cos(th3) - t * Math.sin(th3)) - Math.sin(fi) * ry * (Math.sin(th3) + t * Math.cos(th3)),
            c1y = cy + Math.sin(fi) * rx * (Math.cos(th3) - t * Math.sin(th3)) + Math.cos(fi) * ry * (Math.sin(th3) + t * Math.cos(th3)),
            c2x = cx + Math.cos(fi) * rx * (Math.cos(th4) + t * Math.sin(th4)) - Math.sin(fi) * ry * (Math.sin(th4) - t * Math.cos(th4)),
            c2y = cy + Math.sin(fi) * rx * (Math.cos(th4) + t * Math.sin(th4)) + Math.cos(fi) * ry * (Math.sin(th4) - t * Math.cos(th4)),
            endX = cx + Math.cos(fi) * rx * Math.cos(th4) - Math.sin(fi) * ry * Math.sin(th4),
            endY = cy + Math.sin(fi) * rx * Math.cos(th4) + Math.cos(fi) * ry * Math.sin(th4);
          this.addCommand(['curve', i === 0, i === segms - 1, c1x, c1y, c2x, c2y, endX, endY]);
        }
      }
      lastCom = 'A';
      return this;
    };
    this.a = function (rx, ry, fi, fa, fs, x, y) {
      return this.A(rx, ry, fi, fa, fs, currX + x, currY + y);
    };
    this.path = function (d) {
      var command,
        value,
        temp,
        parser = new StringParser((d || '').trim());
      while (command = parser.match(/^[astvzqmhlcASTVZQMHLC]/)) {
        parser.matchSeparator();
        var values = [];
        while (value = PathFlags[command + values.length] ? parser.match(/^[01]/) : parser.matchNumber()) {
          parser.matchSeparator();
          if (values.length === PathArguments[command]) {
            this[command].apply(this, values);
            values = [];
            if (command === 'M') {
              command = 'L';
            } else if (command === 'm') {
              command = 'l';
            }
          }
          values.push(Number(value));
        }
        if (values.length === PathArguments[command]) {
          this[command].apply(this, values);
        } else {
          warningCallback('SvgPath: command ' + command + ' with ' + values.length + ' numbers');
          return;
        }
      }
      if (temp = parser.matchAll()) {
        warningCallback('SvgPath: unexpected string ' + temp);
      }
      return this;
    };
    this.getBoundingBox = function () {
      var bbox = [Infinity, Infinity, -Infinity, -Infinity];
      function addBounds(bbox1) {
        if (bbox1[0] < bbox[0]) {
          bbox[0] = bbox1[0];
        }
        if (bbox1[2] > bbox[2]) {
          bbox[2] = bbox1[2];
        }
        if (bbox1[1] < bbox[1]) {
          bbox[1] = bbox1[1];
        }
        if (bbox1[3] > bbox[3]) {
          bbox[3] = bbox1[3];
        }
      }
      for (var i = 0; i < this.pathSegments.length; i++) {
        addBounds(this.pathSegments[i].getBoundingBox());
      }
      if (bbox[0] === Infinity) {
        bbox[0] = 0;
      }
      if (bbox[1] === Infinity) {
        bbox[1] = 0;
      }
      if (bbox[2] === -Infinity) {
        bbox[2] = 0;
      }
      if (bbox[3] === -Infinity) {
        bbox[3] = 0;
      }
      return bbox;
    };
    this.getPointAtLength = function (l) {
      if (l >= 0 && l <= this.totalLength) {
        var temp;
        for (var i = 0; i < this.pathSegments.length; i++) {
          if (temp = this.pathSegments[i].getPointAtLength(l)) {
            return temp;
          }
          l -= this.pathSegments[i].totalLength;
        }
        return this.endPoint;
      }
    };
    this.transform = function (m) {
      this.pathSegments = [];
      this.startPoint = null;
      this.endPoint = null;
      this.totalLength = 0;
      for (var i = 0; i < this.pathCommands.length; i++) {
        var data = this.pathCommands.shift();
        for (var j = 3; j < data.length; j += 2) {
          var p = transformPoint([data[j], data[j + 1]], m);
          data[j] = p[0];
          data[j + 1] = p[1];
        }
        this.addCommand(data);
      }
      return this;
    };
    this.mergeShape = function (shape) {
      for (var i = 0; i < shape.pathCommands.length; i++) {
        this.addCommand(shape.pathCommands[i].slice());
      }
      return this;
    };
    this.clone = function () {
      return new _SvgShape().mergeShape(this);
    };
    this.insertInDocument = function () {
      for (var i = 0; i < this.pathCommands.length; i++) {
        var command = this.pathCommands[i][0],
          values = this.pathCommands[i].slice(3);
        switch (command) {
          case 'move':
            doc.moveTo(values[0], values[1]);
            break;
          case 'line':
            doc.lineTo(values[0], values[1]);
            break;
          case 'curve':
            doc.bezierCurveTo(values[0], values[1], values[2], values[3], values[4], values[5]);
            break;
          case 'close':
            doc.closePath();
            break;
        }
      }
    };
    this.getSubPaths = function () {
      var subPaths = [],
        shape = new _SvgShape();
      for (var i = 0; i < this.pathCommands.length; i++) {
        var data = this.pathCommands[i],
          command = this.pathCommands[i][0];
        if (command === 'move' && i !== 0) {
          subPaths.push(shape);
          shape = new _SvgShape();
        }
        shape.addCommand(data);
      }
      subPaths.push(shape);
      return subPaths;
    };
    this.getMarkers = function () {
      var markers = [],
        subPaths = this.getSubPaths();
      for (var i = 0; i < subPaths.length; i++) {
        var subPath = subPaths[i],
          subPathMarkers = [];
        for (var j = 0; j < subPath.pathSegments.length; j++) {
          var segment = subPath.pathSegments[j];
          if (isNotEqual(segment.totalLength, 0) || j === 0 || j === subPath.pathSegments.length - 1) {
            if (segment.hasStart) {
              var startMarker = segment.getPointAtLength(0),
                prevEndMarker = subPathMarkers.pop();
              if (prevEndMarker) {
                startMarker[2] = 0.5 * (prevEndMarker[2] + startMarker[2]);
              }
              subPathMarkers.push(startMarker);
            }
            if (segment.hasEnd) {
              var endMarker = segment.getPointAtLength(segment.totalLength);
              subPathMarkers.push(endMarker);
            }
          }
        }
        markers = markers.concat(subPathMarkers);
      }
      return markers;
    };
  };
  var SvgElem = function SvgElem(obj, inherits) {
    var styleCache = Object.create(null);
    var childrenCache = null;
    this.name = obj.nodeName;
    this.isOuterElement = obj === svg || !obj.parentNode;
    this.inherits = inherits || (!this.isOuterElement ? createSVGElement(obj.parentNode, null) : null);
    this.stack = this.inherits ? this.inherits.stack.concat(obj) : [obj];
    this.style = parseStyleAttr(typeof obj.getAttribute === 'function' && obj.getAttribute('style'));
    this.css = useCSS ? getComputedStyle(obj) : getStyle(obj);
    this.allowedChildren = [];
    this.attr = function (key) {
      if (typeof obj.getAttribute === 'function') {
        return obj.getAttribute(key);
      }
    };
    this.resolveUrl = function (value) {
      var temp = (value || '').match(/^\s*(?:url\("(.*)#(.*)"\)|url\('(.*)#(.*)'\)|url\((.*)#(.*)\)|(.*)#(.*))\s*$/) || [];
      var file = temp[1] || temp[3] || temp[5] || temp[7],
        id = temp[2] || temp[4] || temp[6] || temp[8];
      if (id) {
        if (!file) {
          var svgObj = svg.getElementById(id);
          if (svgObj) {
            if (this.stack.indexOf(svgObj) === -1) {
              return svgObj;
            } else {
              warningCallback('SVGtoPDF: loop of circular references for id "' + id + '"');
              return;
            }
          }
        }
        if (documentCallback) {
          var svgs = documentCache[file];
          if (!svgs) {
            svgs = documentCallback(file);
            if (!isArrayLike(svgs)) {
              svgs = [svgs];
            }
            for (var i = 0; i < svgs.length; i++) {
              if (typeof svgs[i] === 'string') {
                svgs[i] = parseXml(svgs[i]);
              }
            }
            documentCache[file] = svgs;
          }
          for (var _i7 = 0; _i7 < svgs.length; _i7++) {
            var _svgObj = svgs[_i7].getElementById(id);
            if (_svgObj) {
              if (this.stack.indexOf(_svgObj) === -1) {
                return _svgObj;
              } else {
                warningCallback('SVGtoPDF: loop of circular references for id "' + file + '#' + id + '"');
                return;
              }
            }
          }
        }
      }
    };
    this.computeUnits = function (value, unit, percent, isFontSize) {
      if (unit === '%') {
        return parseFloat(value) / 100 * (isFontSize || percent != null ? percent : this.getViewport());
      } else if (unit === 'ex' || unit === 'em') {
        return value * {
          'em': 1,
          'ex': 0.5
        }[unit] * (isFontSize ? percent : this.get('font-size'));
      } else {
        return value * {
          '': 1,
          'px': 1,
          'pt': 96 / 72,
          'cm': 96 / 2.54,
          'mm': 96 / 25.4,
          'in': 96,
          'pc': 96 / 6
        }[unit];
      }
    };
    this.computeLength = function (value, percent, initial, isFontSize) {
      var parser = new StringParser((value || '').trim()),
        temp1,
        temp2;
      if (typeof (temp1 = parser.matchNumber()) === 'string' && typeof (temp2 = parser.matchLengthUnit()) === 'string' && !parser.matchAll()) {
        return this.computeUnits(temp1, temp2, percent, isFontSize);
      }
      return initial;
    };
    this.computeLengthList = function (value, percent, strict) {
      var parser = new StringParser((value || '').trim()),
        result = [],
        temp1,
        temp2;
      while (typeof (temp1 = parser.matchNumber()) === 'string' && typeof (temp2 = parser.matchLengthUnit()) === 'string') {
        result.push(this.computeUnits(temp1, temp2, percent));
        parser.matchSeparator();
      }
      if (strict && parser.matchAll()) {
        return;
      }
      return result;
    };
    this.getLength = function (key, percent, initial) {
      return this.computeLength(this.attr(key), percent, initial);
    };
    this.getLengthList = function (key, percent) {
      return this.computeLengthList(this.attr(key), percent);
    };
    this.getUrl = function (key) {
      return this.resolveUrl(this.attr(key));
    };
    this.getNumberList = function (key) {
      var parser = new StringParser((this.attr(key) || '').trim()),
        result = [],
        temp;
      while (temp = parser.matchNumber()) {
        result.push(Number(temp));
        parser.matchSeparator();
      }
      result.error = parser.matchAll();
      return result;
    };
    this.getViewbox = function (key, initial) {
      var viewBox = this.getNumberList(key);
      if (viewBox.length === 4 && viewBox[2] >= 0 && viewBox[3] >= 0) {
        return viewBox;
      }
      return initial;
    };
    this.getPercent = function (key, initial) {
      var value = this.attr(key);
      var parser = new StringParser((value || '').trim()),
        temp1,
        temp2;
      var number = parser.matchNumber();
      if (!number) {
        return initial;
      }
      if (parser.match('%')) {
        number *= 0.01;
      }
      if (parser.matchAll()) {
        return initial;
      }
      return Math.max(0, Math.min(1, number));
    };
    this.chooseValue = function (args) {
      for (var i = 0; i < arguments.length; i++) {
        if (arguments[i] != null && arguments[i] === arguments[i]) {
          return arguments[i];
        }
      }
      return arguments[arguments.length - 1];
    };
    this.get = function (key) {
      if (styleCache[key] !== undefined) {
        return styleCache[key];
      }
      var keyInfo = Properties[key] || {},
        value,
        result;
      for (var i = 0; i < 3; i++) {
        switch (i) {
          case 0:
            if (key !== 'transform') {
              // the CSS transform behaves strangely
              value = this.css[keyInfo.css || key];
            }
            break;
          case 1:
            value = this.style[key];
            break;
          case 2:
            value = this.attr(key);
            break;
        }
        if (value === 'inherit') {
          result = this.inherits ? this.inherits.get(key) : keyInfo.initial;
          if (result != null) {
            return styleCache[key] = result;
          }
        }
        if (keyInfo.values != null) {
          result = keyInfo.values[value];
          if (result != null) {
            return styleCache[key] = result;
          }
        }
        if (value != null) {
          var parsed = void 0;
          switch (key) {
            case 'font-size':
              result = this.computeLength(value, this.inherits ? this.inherits.get(key) : keyInfo.initial, undefined, true);
              break;
            case 'baseline-shift':
              result = this.computeLength(value, this.get('font-size'));
              break;
            case 'font-family':
              result = value || undefined;
              break;
            case 'opacity':
            case 'stroke-opacity':
            case 'fill-opacity':
            case 'stop-opacity':
              parsed = parseFloat(value);
              if (!isNaN(parsed)) {
                result = Math.max(0, Math.min(1, parsed));
              }
              break;
            case 'transform':
              result = parseTranform(value);
              break;
            case 'stroke-dasharray':
              if (value === 'none') {
                result = [];
              } else if (parsed = this.computeLengthList(value, this.getViewport(), true)) {
                var sum = 0,
                  error = false;
                for (var j = 0; j < parsed.length; j++) {
                  if (parsed[j] < 0) {
                    error = true;
                  }
                  sum += parsed[j];
                }
                if (!error) {
                  if (parsed.length % 2 === 1) {
                    parsed = parsed.concat(parsed);
                  }
                  result = sum === 0 ? [] : parsed;
                }
              }
              break;
            case 'color':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else {
                result = parseColor(value);
              }
              break;
            case 'fill':
            case 'stroke':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else if (value === 'currentColor') {
                result = this.get('color');
              } else if (parsed = parseColor(value)) {
                return parsed;
              } else if (parsed = (value || '').split(' ')) {
                var object = this.resolveUrl(parsed[0]),
                  fallbackColor = parseColor(parsed[1]);
                if (object == null) {
                  result = fallbackColor;
                } else if (object.nodeName === 'linearGradient' || object.nodeName === 'radialGradient') {
                  result = new _SvgElemGradient(object, null, fallbackColor);
                } else if (object.nodeName === 'pattern') {
                  result = new _SvgElemPattern(object, null, fallbackColor);
                } else {
                  result = fallbackColor;
                }
              }
              break;
            case 'stop-color':
              if (value === 'none' || value === 'transparent') {
                result = 'none';
              } else if (value === 'currentColor') {
                result = this.get('color');
              } else {
                result = parseColor(value);
              }
              break;
            case 'marker-start':
            case 'marker-mid':
            case 'marker-end':
            case 'clip-path':
            case 'mask':
              if (value === 'none') {
                result = 'none';
              } else {
                result = this.resolveUrl(value);
              }
              break;
            case 'stroke-width':
              parsed = this.computeLength(value, this.getViewport());
              if (parsed != null && parsed >= 0) {
                result = parsed;
              }
              break;
            case 'stroke-miterlimit':
              parsed = parseFloat(value);
              if (parsed != null && parsed >= 1) {
                result = parsed;
              }
              break;
            case 'word-spacing':
            case 'letter-spacing':
              result = this.computeLength(value, this.getViewport());
              break;
            case 'stroke-dashoffset':
              result = this.computeLength(value, this.getViewport());
              if (result != null) {
                if (result < 0) {
                  // fix for crbug.com/660850
                  var dasharray = this.get('stroke-dasharray');
                  for (var _j = 0; _j < dasharray.length; _j++) {
                    result += dasharray[_j];
                  }
                }
              }
              break;
          }
          if (result != null) {
            return styleCache[key] = result;
          }
        }
      }
      return styleCache[key] = keyInfo.inherit && this.inherits ? this.inherits.get(key) : keyInfo.initial;
    };
    this.getChildren = function () {
      if (childrenCache != null) {
        return childrenCache;
      }
      var children = [];
      for (var i = 0; i < obj.childNodes.length; i++) {
        var child = obj.childNodes[i];
        if (!child.error && this.allowedChildren.indexOf(child.nodeName) !== -1) {
          children.push(createSVGElement(child, this));
        }
      }
      return childrenCache = children;
    };
    this.getParentVWidth = function () {
      return this.inherits ? this.inherits.getVWidth() : viewportWidth;
    };
    this.getParentVHeight = function () {
      return this.inherits ? this.inherits.getVHeight() : viewportHeight;
    };
    this.getParentViewport = function () {
      return Math.sqrt(0.5 * this.getParentVWidth() * this.getParentVWidth() + 0.5 * this.getParentVHeight() * this.getParentVHeight());
    };
    this.getVWidth = function () {
      return this.getParentVWidth();
    };
    this.getVHeight = function () {
      return this.getParentVHeight();
    };
    this.getViewport = function () {
      return Math.sqrt(0.5 * this.getVWidth() * this.getVWidth() + 0.5 * this.getVHeight() * this.getVHeight());
    };
    this.getBoundingBox = function () {
      var shape = this.getBoundingShape();
      return shape.getBoundingBox();
    };
  };
  var SvgElemStylable = function SvgElemStylable(obj, inherits) {
    SvgElem.call(this, obj, inherits);
    this.transform = function () {
      doc.transform.apply(doc, this.getTransformation());
    };
    this.clip = function () {
      if (this.get('clip-path') !== 'none') {
        var clipPath = new SvgElemClipPath(this.get('clip-path'), null);
        clipPath.useMask(this.getBoundingBox());
        return true;
      }
    };
    this.mask = function () {
      if (this.get('mask') !== 'none') {
        var mask = new SvgElemMask(this.get('mask'), null);
        mask.useMask(this.getBoundingBox());
        return true;
      }
    };
    this.getFill = function (isClip, isMask) {
      var opacity = this.get('opacity'),
        fill = this.get('fill'),
        fillOpacity = this.get('fill-opacity');
      if (isClip) {
        return DefaultColors.white;
      }
      if (fill !== 'none' && opacity && fillOpacity) {
        if (fill instanceof _SvgElemGradient || fill instanceof _SvgElemPattern) {
          return fill.getPaint(this.getBoundingBox(), fillOpacity * opacity, isClip, isMask);
        }
        return opacityToColor(fill, fillOpacity * opacity, isMask);
      }
    };
    this.getStroke = function (isClip, isMask) {
      var opacity = this.get('opacity'),
        stroke = this.get('stroke'),
        strokeOpacity = this.get('stroke-opacity');
      if (isClip || isEqual(this.get('stroke-width'), 0)) {
        return;
      }
      if (stroke !== 'none' && opacity && strokeOpacity) {
        if (stroke instanceof _SvgElemGradient || stroke instanceof _SvgElemPattern) {
          return stroke.getPaint(this.getBoundingBox(), strokeOpacity * opacity, isClip, isMask);
        }
        return opacityToColor(stroke, strokeOpacity * opacity, isMask);
      }
    };
  };
  var SvgElemHasChildren = function SvgElemHasChildren(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.allowedChildren = ['use', 'g', 'a', 'svg', 'image', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path', 'text'];
    this.getBoundingShape = function () {
      var shape = new _SvgShape(),
        children = this.getChildren();
      for (var i = 0; i < children.length; i++) {
        if (children[i].get('display') !== 'none') {
          if (typeof children[i].getBoundingShape === 'function') {
            var childShape = children[i].getBoundingShape().clone();
            if (typeof children[i].getTransformation === 'function') {
              childShape.transform(children[i].getTransformation());
            }
            shape.mergeShape(childShape);
          }
        }
      }
      return shape;
    };
    this.drawChildren = function (isClip, isMask) {
      var children = this.getChildren();
      for (var i = 0; i < children.length; i++) {
        if (children[i].get('display') !== 'none') {
          if (typeof children[i].drawInDocument === 'function') {
            children[i].drawInDocument(isClip, isMask);
          }
        }
      }
    };
  };
  var SvgElemContainer = function SvgElemContainer(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);
    this.drawContent = function (isClip, isMask) {
      this.transform();
      var clipped = this.clip(),
        masked = this.mask(),
        group;
      if ((this.get('opacity') < 1 || clipped || masked) && !isClip) {
        group = docBeginGroup(getPageBBox());
      }
      this.drawChildren(isClip, isMask);
      if (group) {
        docEndGroup(group);
        doc.fillOpacity(this.get('opacity'));
        docInsertGroup(group);
      }
    };
  };
  var SvgElemUse = function SvgElemUse(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var x = this.getLength('x', this.getVWidth(), 0),
      y = this.getLength('y', this.getVHeight(), 0),
      child = this.getUrl('href') || this.getUrl('xlink:href');
    if (child) {
      child = createSVGElement(child, this);
    }
    this.getChildren = function () {
      return child ? [child] : [];
    };
    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.drawContent(isClip, isMask);
      doc.restore();
    };
    this.getTransformation = function () {
      return multiplyMatrix(this.get('transform'), [1, 0, 0, 1, x, y]);
    };
  };
  var SvgElemSymbol = function SvgElemSymbol(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var width = this.getLength('width', this.getParentVWidth(), this.getParentVWidth()),
      height = this.getLength('height', this.getParentVHeight(), this.getParentVHeight());
    if (inherits instanceof SvgElemUse) {
      width = inherits.getLength('width', inherits.getParentVWidth(), width);
      height = inherits.getLength('height', inherits.getParentVHeight(), height);
    }
    var aspectRatio = (this.attr('preserveAspectRatio') || '').trim(),
      viewBox = this.getViewbox('viewBox', [0, 0, width, height]);
    this.getVWidth = function () {
      return viewBox[2];
    };
    this.getVHeight = function () {
      return viewBox[3];
    };
    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.drawContent(isClip, isMask);
      doc.restore();
    };
    this.getTransformation = function () {
      return multiplyMatrix(parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3]), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]);
    };
  };
  var SvgElemGroup = function SvgElemGroup(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      if (this.link && !isClip && !isMask) {
        this.addLink();
      }
      this.drawContent(isClip, isMask);
      doc.restore();
    };
    this.getTransformation = function () {
      return this.get('transform');
    };
  };
  var SvgElemLink = function SvgElemLink(obj, inherits) {
    if (inherits && inherits.isText) {
      SvgElemTspan.call(this, obj, inherits);
      this.allowedChildren = ['textPath', 'tspan', '#text', '#cdata-section', 'a'];
    } else {
      SvgElemGroup.call(this, obj, inherits);
    }
    this.link = this.attr('href') || this.attr('xlink:href');
    this.addLink = function () {
      if (this.link.match(/^(?:[a-z][a-z0-9+.-]*:|\/\/)?/i) && this.getChildren().length) {
        var bbox = this.getBoundingShape().transform(getGlobalMatrix()).getBoundingBox();
        docInsertLink(bbox[0], bbox[1], bbox[2], bbox[3], this.link);
      }
    };
  };
  var SvgElemSvg = function SvgElemSvg(obj, inherits) {
    SvgElemContainer.call(this, obj, inherits);
    var width = this.getLength('width', this.getParentVWidth(), this.getParentVWidth()),
      height = this.getLength('height', this.getParentVHeight(), this.getParentVHeight()),
      x = this.getLength('x', this.getParentVWidth(), 0),
      y = this.getLength('y', this.getParentVHeight(), 0);
    if (inherits instanceof SvgElemUse) {
      width = inherits.getLength('width', inherits.getParentVWidth(), width);
      height = inherits.getLength('height', inherits.getParentVHeight(), height);
    }
    var aspectRatio = this.attr('preserveAspectRatio'),
      viewBox = this.getViewbox('viewBox', [0, 0, width, height]);
    if (this.isOuterElement && preserveAspectRatio) {
      x = y = 0;
      width = viewportWidth;
      height = viewportHeight;
      aspectRatio = preserveAspectRatio;
    }
    this.getVWidth = function () {
      return viewBox[2];
    };
    this.getVHeight = function () {
      return viewBox[3];
    };
    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      if (this.get('overflow') === 'hidden') {
        new _SvgShape().M(x, y).L(x + width, y).L(x + width, y + height).L(x, y + height).Z().transform(this.get('transform')).insertInDocument();
        doc.clip();
      }
      this.drawContent(isClip, isMask);
      doc.restore();
    };
    this.getTransformation = function () {
      return multiplyMatrix(this.get('transform'), [1, 0, 0, 1, x, y], parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3]), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]);
    };
  };
  var SVGElemImage = function SVGElemImage(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    var link = imageCallback(this.attr('href') || this.attr('xlink:href') || ''),
      x = this.getLength('x', this.getVWidth(), 0),
      y = this.getLength('y', this.getVHeight(), 0),
      width = this.getLength('width', this.getVWidth(), 'auto'),
      height = this.getLength('height', this.getVHeight(), 'auto'),
      image;
    try {
      image = doc.openImage(link);
    } catch (e) {
      warningCallback('SVGElemImage: failed to open image "' + link + '" in PDFKit');
    }
    if (image) {
      if (width === 'auto' && height !== 'auto') {
        width = height * image.width / image.height;
      } else if (height === 'auto' && width !== 'auto') {
        height = width * image.height / image.width;
      } else if (width === 'auto' && height === 'auto') {
        width = image.width;
        height = image.height;
      }
    }
    if (width === 'auto' || width < 0) {
      width = 0;
    }
    if (height === 'auto' || height < 0) {
      height = 0;
    }
    this.getTransformation = function () {
      return this.get('transform');
    };
    this.getBoundingShape = function () {
      return new _SvgShape().M(x, y).L(x + width, y).M(x + width, y + height).L(x, y + height);
    };
    this.drawInDocument = function (isClip, isMask) {
      if (this.get('visibility') === 'hidden' || !image) {
        return;
      }
      doc.save();
      this.transform();
      if (this.get('overflow') === 'hidden') {
        doc.rect(x, y, width, height).clip();
      }
      this.clip();
      this.mask();
      doc.translate(x, y);
      doc.transform.apply(doc, parseAspectRatio(this.attr('preserveAspectRatio'), width, height, image ? image.width : width, image ? image.height : height));
      if (!isClip) {
        doc.fillOpacity(this.get('opacity'));
        doc.image(image, 0, 0);
      } else {
        doc.rect(0, 0, image.width, image.height);
        docFillColor(DefaultColors.white).fill();
      }
      doc.restore();
    };
  };
  var _SvgElemPattern = function SvgElemPattern(obj, inherits, fallback) {
    SvgElemHasChildren.call(this, obj, inherits);
    this.ref = function () {
      var ref = this.getUrl('href') || this.getUrl('xlink:href');
      if (ref && ref.nodeName === obj.nodeName) {
        return new _SvgElemPattern(ref, inherits, fallback);
      }
    }.call(this);
    var _attr = this.attr;
    this.attr = function (key) {
      var attr = _attr.call(this, key);
      if (attr != null || key === 'href' || key === 'xlink:href') {
        return attr;
      }
      return this.ref ? this.ref.attr(key) : null;
    };
    var _getChildren = this.getChildren;
    this.getChildren = function () {
      var children = _getChildren.call(this);
      if (children.length > 0) {
        return children;
      }
      return this.ref ? this.ref.getChildren() : [];
    };
    this.getPaint = function (bBox, gOpacity, isClip, isMask) {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
        bBoxUnitsContent = this.attr('patternContentUnits') === 'objectBoundingBox',
        x = this.getLength('x', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0),
        y = this.getLength('y', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0),
        width = this.getLength('width', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0),
        height = this.getLength('height', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0);
      if (bBoxUnitsContent && !bBoxUnitsPattern) {
        // Use the same units for pattern & pattern content
        x = (x - bBox[0]) / (bBox[2] - bBox[0]) || 0;
        y = (y - bBox[1]) / (bBox[3] - bBox[1]) || 0;
        width = width / (bBox[2] - bBox[0]) || 0;
        height = height / (bBox[3] - bBox[1]) || 0;
      } else if (!bBoxUnitsContent && bBoxUnitsPattern) {
        x = bBox[0] + x * (bBox[2] - bBox[0]);
        y = bBox[1] + y * (bBox[3] - bBox[1]);
        width = width * (bBox[2] - bBox[0]);
        height = height * (bBox[3] - bBox[1]);
      }
      var viewBox = this.getViewbox('viewBox', [0, 0, width, height]),
        aspectRatio = (this.attr('preserveAspectRatio') || '').trim(),
        aspectRatioMatrix = multiplyMatrix(parseAspectRatio(aspectRatio, width, height, viewBox[2], viewBox[3], 0), [1, 0, 0, 1, -viewBox[0], -viewBox[1]]),
        matrix = parseTranform(this.attr('patternTransform'));
      if (bBoxUnitsContent) {
        matrix = multiplyMatrix([bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]], matrix);
      }
      matrix = multiplyMatrix(matrix, [1, 0, 0, 1, x, y]);
      if ((matrix = validateMatrix(matrix)) && (aspectRatioMatrix = validateMatrix(aspectRatioMatrix)) && (width = validateNumber(width)) && (height = validateNumber(height))) {
        var group = docBeginGroup([0, 0, width, height]);
        doc.transform.apply(doc, aspectRatioMatrix);
        this.drawChildren(isClip, isMask);
        docEndGroup(group);
        return [docCreatePattern(group, width, height, matrix), gOpacity];
      } else {
        return fallback ? [fallback[0], fallback[1] * gOpacity] : undefined;
      }
    };
    this.getVWidth = function () {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
        width = this.getLength('width', bBoxUnitsPattern ? 1 : this.getParentVWidth(), 0);
      return this.getViewbox('viewBox', [0, 0, width, 0])[2];
    };
    this.getVHeight = function () {
      var bBoxUnitsPattern = this.attr('patternUnits') !== 'userSpaceOnUse',
        height = this.getLength('height', bBoxUnitsPattern ? 1 : this.getParentVHeight(), 0);
      return this.getViewbox('viewBox', [0, 0, 0, height])[3];
    };
  };
  var _SvgElemGradient = function SvgElemGradient(obj, inherits, fallback) {
    SvgElem.call(this, obj, inherits);
    this.allowedChildren = ['stop'];
    this.ref = function () {
      var ref = this.getUrl('href') || this.getUrl('xlink:href');
      if (ref && ref.nodeName === obj.nodeName) {
        return new _SvgElemGradient(ref, inherits, fallback);
      }
    }.call(this);
    var _attr = this.attr;
    this.attr = function (key) {
      var attr = _attr.call(this, key);
      if (attr != null || key === 'href' || key === 'xlink:href') {
        return attr;
      }
      return this.ref ? this.ref.attr(key) : null;
    };
    var _getChildren = this.getChildren;
    this.getChildren = function () {
      var children = _getChildren.call(this);
      if (children.length > 0) {
        return children;
      }
      return this.ref ? this.ref.getChildren() : [];
    };
    this.getPaint = function (bBox, gOpacity, isClip, isMask) {
      var children = this.getChildren();
      if (children.length === 0) {
        return;
      }
      if (children.length === 1) {
        var child = children[0],
          stopColor = child.get('stop-color');
        if (stopColor === 'none') {
          return;
        }
        return opacityToColor(stopColor, child.get('stop-opacity') * gOpacity, isMask);
      }
      var bBoxUnits = this.attr('gradientUnits') !== 'userSpaceOnUse',
        matrix = parseTranform(this.attr('gradientTransform')),
        spread = this.attr('spreadMethod'),
        grad,
        x1,
        x2,
        y1,
        y2,
        r2,
        nAfter = 0,
        nBefore = 0,
        nTotal = 1;
      if (bBoxUnits) {
        matrix = multiplyMatrix([bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]], matrix);
      }
      if (matrix = validateMatrix(matrix)) {
        if (this.name === 'linearGradient') {
          x1 = this.getLength('x1', bBoxUnits ? 1 : this.getVWidth(), 0);
          x2 = this.getLength('x2', bBoxUnits ? 1 : this.getVWidth(), bBoxUnits ? 1 : this.getVWidth());
          y1 = this.getLength('y1', bBoxUnits ? 1 : this.getVHeight(), 0);
          y2 = this.getLength('y2', bBoxUnits ? 1 : this.getVHeight(), 0);
        } else {
          x2 = this.getLength('cx', bBoxUnits ? 1 : this.getVWidth(), bBoxUnits ? 0.5 : 0.5 * this.getVWidth());
          y2 = this.getLength('cy', bBoxUnits ? 1 : this.getVHeight(), bBoxUnits ? 0.5 : 0.5 * this.getVHeight());
          r2 = this.getLength('r', bBoxUnits ? 1 : this.getViewport(), bBoxUnits ? 0.5 : 0.5 * this.getViewport());
          x1 = this.getLength('fx', bBoxUnits ? 1 : this.getVWidth(), x2);
          y1 = this.getLength('fy', bBoxUnits ? 1 : this.getVHeight(), y2);
          if (r2 < 0) {
            warningCallback('SvgElemGradient: negative r value');
          }
          var d = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)),
            multiplier = 1;
          if (d > r2) {
            // according to specification
            multiplier = r2 / d;
            x1 = x2 + (x1 - x2) * multiplier;
            y1 = y2 + (y1 - y2) * multiplier;
          }
          r2 = Math.max(r2, d * multiplier * (1 + 1e-6)); // fix for edge-case gradients see issue #84
        }
        if (spread === 'reflect' || spread === 'repeat') {
          var inv = inverseMatrix(matrix),
            corner1 = transformPoint([bBox[0], bBox[1]], inv),
            corner2 = transformPoint([bBox[2], bBox[1]], inv),
            corner3 = transformPoint([bBox[2], bBox[3]], inv),
            corner4 = transformPoint([bBox[0], bBox[3]], inv);
          if (this.name === 'linearGradient') {
            // See file 'gradient-repeat-maths.png'
            nAfter = Math.max((corner1[0] - x2) * (x2 - x1) + (corner1[1] - y2) * (y2 - y1), (corner2[0] - x2) * (x2 - x1) + (corner2[1] - y2) * (y2 - y1), (corner3[0] - x2) * (x2 - x1) + (corner3[1] - y2) * (y2 - y1), (corner4[0] - x2) * (x2 - x1) + (corner4[1] - y2) * (y2 - y1)) / (Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
            nBefore = Math.max((corner1[0] - x1) * (x1 - x2) + (corner1[1] - y1) * (y1 - y2), (corner2[0] - x1) * (x1 - x2) + (corner2[1] - y1) * (y1 - y2), (corner3[0] - x1) * (x1 - x2) + (corner3[1] - y1) * (y1 - y2), (corner4[0] - x1) * (x1 - x2) + (corner4[1] - y1) * (y1 - y2)) / (Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
          } else {
            nAfter = Math.sqrt(Math.max(Math.pow(corner1[0] - x2, 2) + Math.pow(corner1[1] - y2, 2), Math.pow(corner2[0] - x2, 2) + Math.pow(corner2[1] - y2, 2), Math.pow(corner3[0] - x2, 2) + Math.pow(corner3[1] - y2, 2), Math.pow(corner4[0] - x2, 2) + Math.pow(corner4[1] - y2, 2))) / r2 - 1;
          }
          nAfter = Math.ceil(nAfter + 0.5); // Add a little more because the stroke can extend outside of the bounding box
          nBefore = Math.ceil(nBefore + 0.5);
          nTotal = nBefore + 1 + nAfter; // How many times the gradient needs to be repeated to fill the object bounding box
        }
        if (this.name === 'linearGradient') {
          grad = doc.linearGradient(x1 - nBefore * (x2 - x1), y1 - nBefore * (y2 - y1), x2 + nAfter * (x2 - x1), y2 + nAfter * (y2 - y1));
        } else {
          grad = doc.radialGradient(x1, y1, 0, x2, y2, r2 + nAfter * r2);
        }
        for (var n = 0; n < nTotal; n++) {
          var offset = 0,
            inOrder = spread !== 'reflect' || (n - nBefore) % 2 === 0;
          for (var i = 0; i < children.length; i++) {
            var _child = children[inOrder ? i : children.length - 1 - i],
              _stopColor = _child.get('stop-color');
            if (_stopColor === 'none') {
              _stopColor = DefaultColors.transparent;
            }
            _stopColor = opacityToColor(_stopColor, _child.get('stop-opacity') * gOpacity, isMask);
            offset = Math.max(offset, inOrder ? _child.getPercent('offset', 0) : 1 - _child.getPercent('offset', 0));
            if (i === 0 && _stopColor[0].length === 4) {
              grad._colorSpace = 'DeviceCMYK';
            } // Fix until PR #763 is merged into PDFKit
            if (i === 0 && offset > 0) {
              grad.stop((n + 0) / nTotal, _stopColor[0], _stopColor[1]);
            }
            grad.stop((n + offset) / (nAfter + nBefore + 1), _stopColor[0], _stopColor[1]);
            if (i === children.length - 1 && offset < 1) {
              grad.stop((n + 1) / nTotal, _stopColor[0], _stopColor[1]);
            }
          }
        }
        grad.setTransform.apply(grad, matrix);
        return [grad, 1];
      } else {
        return fallback ? [fallback[0], fallback[1] * gOpacity] : undefined;
      }
    };
  };
  var SvgElemBasicShape = function SvgElemBasicShape(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.dashScale = 1;
    this.getBoundingShape = function () {
      return this.shape;
    };
    this.getTransformation = function () {
      return this.get('transform');
    };
    this.drawInDocument = function (isClip, isMask) {
      if (this.get('visibility') === 'hidden' || !this.shape) {
        return;
      }
      doc.save();
      this.transform();
      this.clip();
      if (!isClip) {
        var masked = this.mask(),
          group;
        if (masked) {
          group = docBeginGroup(getPageBBox());
        }
        var subPaths = this.shape.getSubPaths(),
          fill = this.getFill(isClip, isMask),
          stroke = this.getStroke(isClip, isMask),
          lineWidth = this.get('stroke-width'),
          lineCap = this.get('stroke-linecap');
        if (fill || stroke) {
          if (fill) {
            docFillColor(fill);
          }
          if (stroke) {
            for (var j = 0; j < subPaths.length; j++) {
              if (isEqual(subPaths[j].totalLength, 0)) {
                if ((lineCap === 'square' || lineCap === 'round') && lineWidth > 0) {
                  if (subPaths[j].startPoint && subPaths[j].startPoint.length > 1) {
                    var _x4 = subPaths[j].startPoint[0],
                      _y4 = subPaths[j].startPoint[1];
                    docFillColor(stroke);
                    if (lineCap === 'square') {
                      doc.rect(_x4 - 0.5 * lineWidth, _y4 - 0.5 * lineWidth, lineWidth, lineWidth);
                    } else if (lineCap === 'round') {
                      doc.circle(_x4, _y4, 0.5 * lineWidth);
                    }
                    doc.fill();
                  }
                }
              }
            }
            var dashArray = this.get('stroke-dasharray'),
              dashOffset = this.get('stroke-dashoffset');
            if (isNotEqual(this.dashScale, 1)) {
              for (var _j2 = 0; _j2 < dashArray.length; _j2++) {
                dashArray[_j2] *= this.dashScale;
              }
              dashOffset *= this.dashScale;
            }
            docStrokeColor(stroke);
            doc.lineWidth(lineWidth).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(lineCap).dash(dashArray, {
              phase: dashOffset
            });
          }
          for (var _j3 = 0; _j3 < subPaths.length; _j3++) {
            if (subPaths[_j3].totalLength > 0) {
              subPaths[_j3].insertInDocument();
            }
          }
          if (fill && stroke) {
            doc.fillAndStroke(this.get('fill-rule'));
          } else if (fill) {
            doc.fill(this.get('fill-rule'));
          } else if (stroke) {
            doc.stroke();
          }
        }
        var markerStart = this.get('marker-start'),
          markerMid = this.get('marker-mid'),
          markerEnd = this.get('marker-end');
        if (markerStart !== 'none' || markerMid !== 'none' || markerEnd !== 'none') {
          var markersPos = this.shape.getMarkers();
          if (markerStart !== 'none') {
            var marker = new SvgElemMarker(markerStart, null);
            marker.drawMarker(false, isMask, markersPos[0], lineWidth);
          }
          if (markerMid !== 'none') {
            for (var i = 1; i < markersPos.length - 1; i++) {
              var _marker = new SvgElemMarker(markerMid, null);
              _marker.drawMarker(false, isMask, markersPos[i], lineWidth);
            }
          }
          if (markerEnd !== 'none') {
            var _marker2 = new SvgElemMarker(markerEnd, null);
            _marker2.drawMarker(false, isMask, markersPos[markersPos.length - 1], lineWidth);
          }
        }
        if (group) {
          docEndGroup(group);
          docInsertGroup(group);
        }
      } else {
        this.shape.insertInDocument();
        docFillColor(DefaultColors.white);
        doc.fill(this.get('clip-rule'));
      }
      doc.restore();
    };
  };
  var SvgElemRect = function SvgElemRect(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var x = this.getLength('x', this.getVWidth(), 0),
      y = this.getLength('y', this.getVHeight(), 0),
      w = this.getLength('width', this.getVWidth(), 0),
      h = this.getLength('height', this.getVHeight(), 0),
      rx = this.getLength('rx', this.getVWidth()),
      ry = this.getLength('ry', this.getVHeight());
    if (rx === undefined && ry === undefined) {
      rx = ry = 0;
    } else if (rx === undefined && ry !== undefined) {
      rx = ry;
    } else if (rx !== undefined && ry === undefined) {
      ry = rx;
    }
    if (w > 0 && h > 0) {
      if (rx && ry) {
        rx = Math.min(rx, 0.5 * w);
        ry = Math.min(ry, 0.5 * h);
        this.shape = new _SvgShape().M(x + rx, y).L(x + w - rx, y).A(rx, ry, 0, 0, 1, x + w, y + ry).L(x + w, y + h - ry).A(rx, ry, 0, 0, 1, x + w - rx, y + h).L(x + rx, y + h).A(rx, ry, 0, 0, 1, x, y + h - ry).L(x, y + ry).A(rx, ry, 0, 0, 1, x + rx, y).Z();
      } else {
        this.shape = new _SvgShape().M(x, y).L(x + w, y).L(x + w, y + h).L(x, y + h).Z();
      }
    } else {
      this.shape = new _SvgShape();
    }
  };
  var SvgElemCircle = function SvgElemCircle(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var cx = this.getLength('cx', this.getVWidth(), 0),
      cy = this.getLength('cy', this.getVHeight(), 0),
      r = this.getLength('r', this.getViewport(), 0);
    if (r > 0) {
      this.shape = new _SvgShape().M(cx + r, cy).A(r, r, 0, 0, 1, cx - r, cy).A(r, r, 0, 0, 1, cx + r, cy).Z();
    } else {
      this.shape = new _SvgShape();
    }
  };
  var SvgElemEllipse = function SvgElemEllipse(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var cx = this.getLength('cx', this.getVWidth(), 0),
      cy = this.getLength('cy', this.getVHeight(), 0),
      rx = this.getLength('rx', this.getVWidth(), 0),
      ry = this.getLength('ry', this.getVHeight(), 0);
    if (rx > 0 && ry > 0) {
      this.shape = new _SvgShape().M(cx + rx, cy).A(rx, ry, 0, 0, 1, cx - rx, cy).A(rx, ry, 0, 0, 1, cx + rx, cy).Z();
    } else {
      this.shape = new _SvgShape();
    }
  };
  var SvgElemLine = function SvgElemLine(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var x1 = this.getLength('x1', this.getVWidth(), 0),
      y1 = this.getLength('y1', this.getVHeight(), 0),
      x2 = this.getLength('x2', this.getVWidth(), 0),
      y2 = this.getLength('y2', this.getVHeight(), 0);
    this.shape = new _SvgShape().M(x1, y1).L(x2, y2);
  };
  var SvgElemPolyline = function SvgElemPolyline(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var points = this.getNumberList('points');
    this.shape = new _SvgShape();
    for (var i = 0; i < points.length - 1; i += 2) {
      if (i === 0) {
        this.shape.M(points[i], points[i + 1]);
      } else {
        this.shape.L(points[i], points[i + 1]);
      }
    }
    if (points.error) {
      warningCallback('SvgElemPolygon: unexpected string ' + points.error);
    }
    if (points.length % 2 === 1) {
      warningCallback('SvgElemPolyline: uneven number of coordinates');
    }
  };
  var SvgElemPolygon = function SvgElemPolygon(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    var points = this.getNumberList('points');
    this.shape = new _SvgShape();
    for (var i = 0; i < points.length - 1; i += 2) {
      if (i === 0) {
        this.shape.M(points[i], points[i + 1]);
      } else {
        this.shape.L(points[i], points[i + 1]);
      }
    }
    this.shape.Z();
    if (points.error) {
      warningCallback('SvgElemPolygon: unexpected string ' + points.error);
    }
    if (points.length % 2 === 1) {
      warningCallback('SvgElemPolygon: uneven number of coordinates');
    }
  };
  var SvgElemPath = function SvgElemPath(obj, inherits) {
    SvgElemBasicShape.call(this, obj, inherits);
    this.shape = new _SvgShape().path(this.attr('d'));
    var pathLength = this.getLength('pathLength', this.getViewport());
    this.pathLength = pathLength > 0 ? pathLength : undefined;
    this.dashScale = this.pathLength !== undefined ? this.shape.totalLength / this.pathLength : 1;
  };
  var SvgElemMarker = function SvgElemMarker(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);
    var width = this.getLength('markerWidth', this.getParentVWidth(), 3),
      height = this.getLength('markerHeight', this.getParentVHeight(), 3),
      viewBox = this.getViewbox('viewBox', [0, 0, width, height]);
    this.getVWidth = function () {
      return viewBox[2];
    };
    this.getVHeight = function () {
      return viewBox[3];
    };
    this.drawMarker = function (isClip, isMask, posArray, strokeWidth) {
      doc.save();
      var orient = this.attr('orient'),
        units = this.attr('markerUnits'),
        rotate = orient === 'auto' ? posArray[2] : (parseFloat(orient) || 0) * Math.PI / 180,
        scale = units === 'userSpaceOnUse' ? 1 : strokeWidth;
      doc.transform(Math.cos(rotate) * scale, Math.sin(rotate) * scale, -Math.sin(rotate) * scale, Math.cos(rotate) * scale, posArray[0], posArray[1]);
      var refX = this.getLength('refX', this.getVWidth(), 0),
        refY = this.getLength('refY', this.getVHeight(), 0),
        aspectRatioMatrix = parseAspectRatio(this.attr('preserveAspectRatio'), width, height, viewBox[2], viewBox[3], 0.5);
      if (this.get('overflow') === 'hidden') {
        doc.rect(aspectRatioMatrix[0] * (viewBox[0] + viewBox[2] / 2 - refX) - width / 2, aspectRatioMatrix[3] * (viewBox[1] + viewBox[3] / 2 - refY) - height / 2, width, height).clip();
      }
      doc.transform.apply(doc, aspectRatioMatrix);
      doc.translate(-refX, -refY);
      var group;
      if (this.get('opacity') < 1 && !isClip) {
        group = docBeginGroup(getPageBBox());
      }
      this.drawChildren(isClip, isMask);
      if (group) {
        docEndGroup(group);
        doc.fillOpacity(this.get('opacity'));
        docInsertGroup(group);
      }
      doc.restore();
    };
  };
  var SvgElemClipPath = function SvgElemClipPath(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);
    this.useMask = function (bBox) {
      var group = docBeginGroup(getPageBBox());
      doc.save();
      if (this.attr('clipPathUnits') === 'objectBoundingBox') {
        doc.transform(bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]);
      }
      this.clip();
      this.drawChildren(true, false);
      doc.restore();
      docEndGroup(group);
      docApplyMask(group, true);
    };
  };
  var SvgElemMask = function SvgElemMask(obj, inherits) {
    SvgElemHasChildren.call(this, obj, inherits);
    this.useMask = function (bBox) {
      var group = docBeginGroup(getPageBBox());
      doc.save();
      var x, y, w, h;
      if (this.attr('maskUnits') === 'userSpaceOnUse') {
        x = this.getLength('x', this.getVWidth(), -0.1 * (bBox[2] - bBox[0]) + bBox[0]);
        y = this.getLength('y', this.getVHeight(), -0.1 * (bBox[3] - bBox[1]) + bBox[1]);
        w = this.getLength('width', this.getVWidth(), 1.2 * (bBox[2] - bBox[0]));
        h = this.getLength('height', this.getVHeight(), 1.2 * (bBox[3] - bBox[1]));
      } else {
        x = this.getLength('x', this.getVWidth(), -0.1) * (bBox[2] - bBox[0]) + bBox[0];
        y = this.getLength('y', this.getVHeight(), -0.1) * (bBox[3] - bBox[1]) + bBox[1];
        w = this.getLength('width', this.getVWidth(), 1.2) * (bBox[2] - bBox[0]);
        h = this.getLength('height', this.getVHeight(), 1.2) * (bBox[3] - bBox[1]);
      }
      doc.rect(x, y, w, h).clip();
      if (this.attr('maskContentUnits') === 'objectBoundingBox') {
        doc.transform(bBox[2] - bBox[0], 0, 0, bBox[3] - bBox[1], bBox[0], bBox[1]);
      }
      this.clip();
      this.drawChildren(false, true);
      doc.restore();
      docEndGroup(group);
      docApplyMask(group, true);
    };
  };
  var SvgElemTextContainer = function SvgElemTextContainer(obj, inherits) {
    SvgElemStylable.call(this, obj, inherits);
    this.allowedChildren = ['tspan', '#text', '#cdata-section', 'a'];
    this.isText = true;
    this.getBoundingShape = function () {
      var shape = new _SvgShape();
      for (var i = 0; i < this._pos.length; i++) {
        var pos = this._pos[i];
        if (!pos.hidden) {
          var dx0 = pos.ascent * Math.sin(pos.rotate),
            dy0 = -pos.ascent * Math.cos(pos.rotate),
            dx1 = pos.descent * Math.sin(pos.rotate),
            dy1 = -pos.descent * Math.cos(pos.rotate),
            dx2 = pos.width * Math.cos(pos.rotate),
            dy2 = pos.width * Math.sin(pos.rotate);
          shape.M(pos.x + dx0, pos.y + dy0).L(pos.x + dx0 + dx2, pos.y + dy0 + dy2).M(pos.x + dx1 + dx2, pos.y + dy1 + dy2).L(pos.x + dx1, pos.y + dy1);
        }
      }
      return shape;
    };
    this.drawTextInDocument = function (isClip, isMask) {
      if (this.link && !isClip && !isMask) {
        this.addLink();
      }
      if (this.get('text-decoration') === 'underline') {
        this.decorate(0.05 * this._font.size, -0.075 * this._font.size, isClip, isMask);
      }
      if (this.get('text-decoration') === 'overline') {
        this.decorate(0.05 * this._font.size, getAscent(this._font.font, this._font.size) + 0.075 * this._font.size, isClip, isMask);
      }
      var fill = this.getFill(isClip, isMask),
        stroke = this.getStroke(isClip, isMask),
        strokeWidth = this.get('stroke-width');
      if (this._font.fauxBold) {
        if (!stroke) {
          stroke = fill;
          strokeWidth = this._font.size * 0.03;
        } else {
          strokeWidth += this._font.size * 0.03;
        }
      }
      var children = this.getChildren();
      for (var i = 0; i < children.length; i++) {
        var childElem = children[i];
        switch (childElem.name) {
          case 'tspan':
          case 'textPath':
          case 'a':
            if (childElem.get('display') !== 'none') {
              childElem.drawTextInDocument(isClip, isMask);
            }
            break;
          case '#text':
          case '#cdata-section':
            if (this.get('visibility') === 'hidden') {
              continue;
            }
            if (fill || stroke || isClip) {
              if (fill) {
                docFillColor(fill);
              }
              if (stroke && strokeWidth) {
                docStrokeColor(stroke);
                doc.lineWidth(strokeWidth).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(this.get('stroke-linecap')).dash(this.get('stroke-dasharray'), {
                  phase: this.get('stroke-dashoffset')
                });
              }
              docBeginText(this._font.font, this._font.size);
              docSetTextMode(!!fill, !!stroke);
              for (var j = 0, pos = childElem._pos; j < pos.length; j++) {
                if (!pos[j].hidden && isNotEqual(pos[j].width, 0)) {
                  var cos = Math.cos(pos[j].rotate),
                    sin = Math.sin(pos[j].rotate),
                    skew = this._font.fauxItalic ? -0.25 : 0;
                  docSetTextMatrix(cos * pos[j].scale, sin * pos[j].scale, cos * skew - sin, sin * skew + cos, pos[j].x, pos[j].y);
                  docWriteGlyph(pos[j].glyph);
                }
              }
              docEndText();
            }
            break;
        }
      }
      if (this.get('text-decoration') === 'line-through') {
        this.decorate(0.05 * this._font.size, 0.5 * (getAscent(this._font.font, this._font.size) + getDescent(this._font.font, this._font.size)), isClip, isMask);
      }
    };
    this.decorate = function (lineWidth, linePosition, isClip, isMask) {
      var fill = this.getFill(isClip, isMask),
        stroke = this.getStroke(isClip, isMask);
      if (fill) {
        docFillColor(fill);
      }
      if (stroke) {
        docStrokeColor(stroke);
        doc.lineWidth(this.get('stroke-width')).miterLimit(this.get('stroke-miterlimit')).lineJoin(this.get('stroke-linejoin')).lineCap(this.get('stroke-linecap')).dash(this.get('stroke-dasharray'), {
          phase: this.get('stroke-dashoffset')
        });
      }
      for (var j = 0, pos = this._pos; j < pos.length; j++) {
        if (!pos[j].hidden && isNotEqual(pos[j].width, 0)) {
          var dx0 = (linePosition + lineWidth / 2) * Math.sin(pos[j].rotate),
            dy0 = -(linePosition + lineWidth / 2) * Math.cos(pos[j].rotate),
            dx1 = (linePosition - lineWidth / 2) * Math.sin(pos[j].rotate),
            dy1 = -(linePosition - lineWidth / 2) * Math.cos(pos[j].rotate),
            dx2 = pos[j].width * Math.cos(pos[j].rotate),
            dy2 = pos[j].width * Math.sin(pos[j].rotate);
          new _SvgShape().M(pos[j].x + dx0, pos[j].y + dy0).L(pos[j].x + dx0 + dx2, pos[j].y + dy0 + dy2).L(pos[j].x + dx1 + dx2, pos[j].y + dy1 + dy2).L(pos[j].x + dx1, pos[j].y + dy1).Z().insertInDocument();
          if (fill && stroke) {
            doc.fillAndStroke();
          } else if (fill) {
            doc.fill();
          } else if (stroke) {
            doc.stroke();
          }
        }
      }
    };
  };
  var SvgElemTextNode = function SvgElemTextNode(obj, inherits) {
    this.name = obj.nodeName;
    this.textContent = obj.nodeValue;
  };
  var SvgElemTspan = function SvgElemTspan(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
  };
  var SvgElemTextPath = function SvgElemTextPath(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
    var pathObject, pathLength, temp;
    if ((temp = this.attr('path')) && temp.trim() !== '') {
      var _pathLength = this.getLength('pathLength', this.getViewport());
      this.pathObject = new _SvgShape().path(temp);
      this.pathLength = _pathLength > 0 ? _pathLength : this.pathObject.totalLength;
      this.pathScale = this.pathObject.totalLength / this.pathLength;
    } else if ((temp = this.getUrl('href') || this.getUrl('xlink:href')) && temp.nodeName === 'path') {
      var pathElem = new SvgElemPath(temp, this);
      this.pathObject = pathElem.shape.clone().transform(pathElem.get('transform'));
      this.pathLength = this.chooseValue(pathElem.pathLength, this.pathObject.totalLength);
      this.pathScale = this.pathObject.totalLength / this.pathLength;
    }
  };
  var SvgElemText = function SvgElemText(obj, inherits) {
    SvgElemTextContainer.call(this, obj, inherits);
    this.allowedChildren = ['textPath', 'tspan', '#text', '#cdata-section', 'a'];
    (function (textParentElem) {
      var processedText = '',
        remainingText = obj.textContent,
        textPaths = [],
        currentChunk = [],
        currentAnchor,
        currentDirection,
        currentX = 0,
        currentY = 0;
      function doAnchoring() {
        if (currentChunk.length) {
          var last = currentChunk[currentChunk.length - 1];
          var first = currentChunk[0];
          var width = last.x + last.width - first.x;
          var anchordx = {
            'startltr': 0,
            'middleltr': 0.5,
            'endltr': 1,
            'startrtl': 1,
            'middlertl': 0.5,
            'endrtl': 0
          }[currentAnchor + currentDirection] * width || 0;
          for (var i = 0; i < currentChunk.length; i++) {
            currentChunk[i].x -= anchordx;
          }
        }
        currentChunk = [];
      }
      function adjustLength(pos, length, spacingAndGlyphs) {
        var firstChar = pos[0],
          lastChar = pos[pos.length - 1],
          startX = firstChar.x,
          endX = lastChar.x + lastChar.width;
        if (spacingAndGlyphs) {
          var textScale = length / (endX - startX);
          if (textScale > 0 && textScale < Infinity) {
            for (var j = 0; j < pos.length; j++) {
              pos[j].x = startX + textScale * (pos[j].x - startX);
              pos[j].scale *= textScale;
              pos[j].width *= textScale;
            }
          }
        } else {
          if (pos.length >= 2) {
            var spaceDiff = (length - (endX - startX)) / (pos.length - 1);
            for (var _j4 = 0; _j4 < pos.length; _j4++) {
              pos[_j4].x += _j4 * spaceDiff;
            }
          }
        }
        currentX += length - (endX - startX);
      }
      function recursive(currentElem, parentElem) {
        currentElem._x = combineArrays(currentElem.getLengthList('x', currentElem.getVWidth()), parentElem ? parentElem._x.slice(parentElem._pos.length) : []);
        currentElem._y = combineArrays(currentElem.getLengthList('y', currentElem.getVHeight()), parentElem ? parentElem._y.slice(parentElem._pos.length) : []);
        currentElem._dx = combineArrays(currentElem.getLengthList('dx', currentElem.getVWidth()), parentElem ? parentElem._dx.slice(parentElem._pos.length) : []);
        currentElem._dy = combineArrays(currentElem.getLengthList('dy', currentElem.getVHeight()), parentElem ? parentElem._dy.slice(parentElem._pos.length) : []);
        currentElem._rot = combineArrays(currentElem.getNumberList('rotate'), parentElem ? parentElem._rot.slice(parentElem._pos.length) : []);
        currentElem._defRot = currentElem.chooseValue(currentElem._rot[currentElem._rot.length - 1], parentElem && parentElem._defRot, 0);
        if (currentElem.name === 'textPath') {
          currentElem._y = [];
        }
        var fontOptions = {
            fauxItalic: false,
            fauxBold: false
          },
          fontNameorLink = fontCallback(currentElem.get('font-family'), currentElem.get('font-weight') === 'bold', currentElem.get('font-style') === 'italic', fontOptions);
        try {
          doc.font(fontNameorLink);
        } catch (e) {
          warningCallback('SVGElemText: failed to open font "' + fontNameorLink + '" in PDFKit');
        }
        currentElem._pos = [];
        currentElem._index = 0;
        currentElem._font = {
          font: doc._font,
          size: currentElem.get('font-size'),
          fauxItalic: fontOptions.fauxItalic,
          fauxBold: fontOptions.fauxBold
        };
        var textLength = currentElem.getLength('textLength', currentElem.getVWidth(), undefined),
          spacingAndGlyphs = currentElem.attr('lengthAdjust') === 'spacingAndGlyphs',
          wordSpacing = currentElem.get('word-spacing'),
          letterSpacing = currentElem.get('letter-spacing'),
          textAnchor = currentElem.get('text-anchor'),
          textDirection = currentElem.get('direction'),
          baseline = getBaseline(currentElem._font.font, currentElem._font.size, currentElem.get('alignment-baseline') || currentElem.get('dominant-baseline'), currentElem.get('baseline-shift'));
        if (currentElem.name === 'textPath') {
          doAnchoring();
          currentX = currentY = 0;
        }
        var children = currentElem.getChildren();
        for (var i = 0; i < children.length; i++) {
          var childElem = children[i];
          switch (childElem.name) {
            case 'tspan':
            case 'textPath':
            case 'a':
              recursive(childElem, currentElem);
              break;
            case '#text':
            case '#cdata-section':
              var rawText = childElem.textContent,
                renderedText = rawText,
                words = void 0;
              childElem._font = currentElem._font;
              childElem._pos = [];
              remainingText = remainingText.substring(rawText.length);
              if (currentElem.get('xml:space') === 'preserve') {
                renderedText = renderedText.replace(/[\s]/g, ' ');
              } else {
                renderedText = renderedText.replace(/[\s]+/g, ' ');
                if (processedText.match(/[\s]$|^$/)) {
                  renderedText = renderedText.replace(/^[\s]/, '');
                }
                if (remainingText.match(/^[\s]*$/)) {
                  renderedText = renderedText.replace(/[\s]$/, '');
                }
              }
              processedText += rawText;
              if (wordSpacing === 0) {
                words = [renderedText];
              } else {
                words = renderedText.split(/(\s)/);
              }
              for (var w = 0; w < words.length; w++) {
                var pos = getTextPos(currentElem._font.font, currentElem._font.size, words[w]);
                for (var j = 0; j < pos.length; j++) {
                  var index = currentElem._index,
                    xAttr = currentElem._x[index],
                    yAttr = currentElem._y[index],
                    dxAttr = currentElem._dx[index],
                    dyAttr = currentElem._dy[index],
                    rotAttr = currentElem._rot[index],
                    continuous = !(w === 0 && j === 0);
                  if (xAttr !== undefined) {
                    continuous = false;
                    doAnchoring();
                    currentX = xAttr;
                  }
                  if (yAttr !== undefined) {
                    continuous = false;
                    doAnchoring();
                    currentY = yAttr;
                  }
                  if (dxAttr !== undefined) {
                    continuous = false;
                    currentX += dxAttr;
                  }
                  if (dyAttr !== undefined) {
                    continuous = false;
                    currentY += dyAttr;
                  }
                  if (rotAttr !== undefined || currentElem._defRot !== 0) {
                    continuous = false;
                  }
                  var position = {
                    glyph: pos[j].glyph,
                    rotate: Math.PI / 180 * currentElem.chooseValue(rotAttr, currentElem._defRot),
                    x: currentX + pos[j].xOffset,
                    y: currentY + baseline + pos[j].yOffset,
                    width: pos[j].width,
                    ascent: getAscent(currentElem._font.font, currentElem._font.size),
                    descent: getDescent(currentElem._font.font, currentElem._font.size),
                    scale: 1,
                    hidden: false,
                    continuous: continuous
                  };
                  currentChunk.push(position);
                  childElem._pos.push(position);
                  currentElem._pos.push(position);
                  currentElem._index += pos[j].unicode.length;
                  if (currentChunk.length === 1) {
                    currentAnchor = textAnchor;
                    currentDirection = textDirection;
                  }
                  currentX += pos[j].xAdvance + letterSpacing;
                  currentY += pos[j].yAdvance;
                }
                if (words[w] === ' ') {
                  currentX += wordSpacing;
                }
              }
              break;
            default:
              remainingText = remainingText.substring(childElem.textContent.length);
          }
        }
        if (textLength && currentElem._pos.length) {
          adjustLength(currentElem._pos, textLength, spacingAndGlyphs);
        }
        if (currentElem.name === 'textPath' || currentElem.name === 'text') {
          doAnchoring();
        }
        if (currentElem.name === 'textPath') {
          textPaths.push(currentElem);
          var pathObject = currentElem.pathObject;
          if (pathObject) {
            currentX = pathObject.endPoint[0];
            currentY = pathObject.endPoint[1];
          }
        }
        if (parentElem) {
          parentElem._pos = parentElem._pos.concat(currentElem._pos);
          parentElem._index += currentElem._index;
        }
      }
      function textOnPath(currentElem) {
        var pathObject = currentElem.pathObject,
          pathLength = currentElem.pathLength,
          pathScale = currentElem.pathScale;
        if (pathObject) {
          var textOffset = currentElem.getLength('startOffset', pathLength, 0);
          for (var j = 0; j < currentElem._pos.length; j++) {
            var charMidX = textOffset + currentElem._pos[j].x + 0.5 * currentElem._pos[j].width;
            if (charMidX > pathLength || charMidX < 0) {
              currentElem._pos[j].hidden = true;
            } else {
              var pointOnPath = pathObject.getPointAtLength(charMidX * pathScale);
              if (isNotEqual(pathScale, 1)) {
                currentElem._pos[j].scale *= pathScale;
                currentElem._pos[j].width *= pathScale;
              }
              currentElem._pos[j].x = pointOnPath[0] - 0.5 * currentElem._pos[j].width * Math.cos(pointOnPath[2]) - currentElem._pos[j].y * Math.sin(pointOnPath[2]);
              currentElem._pos[j].y = pointOnPath[1] - 0.5 * currentElem._pos[j].width * Math.sin(pointOnPath[2]) + currentElem._pos[j].y * Math.cos(pointOnPath[2]);
              currentElem._pos[j].rotate = pointOnPath[2] + currentElem._pos[j].rotate;
              currentElem._pos[j].continuous = false;
            }
          }
        } else {
          for (var _j5 = 0; _j5 < currentElem._pos.length; _j5++) {
            currentElem._pos[_j5].hidden = true;
          }
        }
      }
      recursive(textParentElem, null);
      for (var i = 0; i < textPaths.length; i++) {
        textOnPath(textPaths[i]);
      }
    })(this);
    this.getTransformation = function () {
      return this.get('transform');
    };
    this.drawInDocument = function (isClip, isMask) {
      doc.save();
      this.transform();
      this.clip();
      var masked = this.mask(),
        group;
      if (masked) {
        group = docBeginGroup(getPageBBox());
      }
      this.drawTextInDocument(isClip, isMask);
      if (group) {
        docEndGroup(group);
        docInsertGroup(group);
      }
      doc.restore();
    };
  };
  options = options || {};
  var pxToPt = options.assumePt ? 1 : 72 / 96,
    // 1px = 72/96pt, but only if assumePt is false
    viewportWidth = (options.width || doc.page.width) / pxToPt,
    viewportHeight = (options.height || doc.page.height) / pxToPt,
    preserveAspectRatio = options.preserveAspectRatio || null,
    // default to null so that the attr can override if not passed
    useCSS = options.useCSS && typeof SVGElement !== 'undefined' && svg instanceof SVGElement && typeof getComputedStyle === 'function',
    warningCallback = options.warningCallback,
    fontCallback = options.fontCallback,
    imageCallback = options.imageCallback,
    colorCallback = options.colorCallback,
    documentCallback = options.documentCallback,
    precision = Math.ceil(Math.max(1, options.precision)) || 3,
    groupStack = [],
    documentCache = {},
    links = [],
    styleRules = [];
  if (typeof warningCallback !== 'function') {
    warningCallback = function warningCallback(str) {
      if (typeof console !== undefined && typeof console.warn === 'function') {
        console.warn(str);
      }
    };
  }
  if (typeof fontCallback !== 'function') {
    fontCallback = function fontCallback(family, bold, italic, fontOptions) {
      // Check if the font is already registered in the document
      if (bold && italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-BoldItalic')) {
          return family + '-BoldItalic';
        } else if (doc._registeredFonts.hasOwnProperty(family + '-Italic')) {
          fontOptions.fauxBold = true;
          return family + '-Italic';
        } else if (doc._registeredFonts.hasOwnProperty(family + '-Bold')) {
          fontOptions.fauxItalic = true;
          return family + '-Bold';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxBold = true;
          fontOptions.fauxItalic = true;
          return family;
        }
      }
      if (bold && !italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-Bold')) {
          return family + '-Bold';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxBold = true;
          return family;
        }
      }
      if (!bold && italic) {
        if (doc._registeredFonts.hasOwnProperty(family + '-Italic')) {
          return family + '-Italic';
        } else if (doc._registeredFonts.hasOwnProperty(family)) {
          fontOptions.fauxItalic = true;
          return family;
        }
      }
      if (!bold && !italic) {
        if (doc._registeredFonts.hasOwnProperty(family)) {
          return family;
        }
      }
      // Use standard fonts as fallback
      if (family.match(/(?:^|,)\s*serif\s*$/)) {
        if (bold && italic) {
          return 'Times-BoldItalic';
        }
        if (bold && !italic) {
          return 'Times-Bold';
        }
        if (!bold && italic) {
          return 'Times-Italic';
        }
        if (!bold && !italic) {
          return 'Times-Roman';
        }
      } else if (family.match(/(?:^|,)\s*monospace\s*$/)) {
        if (bold && italic) {
          return 'Courier-BoldOblique';
        }
        if (bold && !italic) {
          return 'Courier-Bold';
        }
        if (!bold && italic) {
          return 'Courier-Oblique';
        }
        if (!bold && !italic) {
          return 'Courier';
        }
      } else if (family.match(/(?:^|,)\s*sans-serif\s*$/) || true) {
        if (bold && italic) {
          return 'Helvetica-BoldOblique';
        }
        if (bold && !italic) {
          return 'Helvetica-Bold';
        }
        if (!bold && italic) {
          return 'Helvetica-Oblique';
        }
        if (!bold && !italic) {
          return 'Helvetica';
        }
      }
    };
  }
  if (typeof imageCallback !== 'function') {
    imageCallback = function imageCallback(link) {
      return link.replace(/\s+/g, '');
    };
  }
  if (typeof colorCallback !== 'function') {
    colorCallback = null;
  } else {
    for (var color in DefaultColors) {
      var newColor = colorCallback(DefaultColors[color]);
      DefaultColors[color][0] = newColor[0];
      DefaultColors[color][1] = newColor[1];
    }
  }
  if (typeof documentCallback !== 'function') {
    documentCallback = null;
  }
  if (typeof svg === 'string') {
    svg = parseXml(svg);
  }
  if (svg) {
    var styles = svg.getElementsByTagName('style');
    for (var i = 0; i < styles.length; i++) {
      styleRules = styleRules.concat(parseStyleSheet(styles[i].textContent));
    }
    var elem = createSVGElement(svg, null);
    if (typeof elem.drawInDocument === 'function') {
      if (options.useCSS && !useCSS) {
        warningCallback('SVGtoPDF: useCSS option can only be used for SVG *elements* in compatible browsers');
      }
      var savedFillColor = doc._fillColor;
      doc.save().translate(x || 0, y || 0).scale(pxToPt);
      elem.drawInDocument();
      for (var _i8 = 0; _i8 < links.length; _i8++) {
        doc.page.annotations.push(links[_i8]);
      }
      doc.restore();
      doc._fillColor = savedFillColor;
    } else {
      warningCallback('SVGtoPDF: this element can\'t be rendered directly: ' + svg.nodeName);
    }
  } else {
    warningCallback('SVGtoPDF: the input does not look like a valid SVG');
  }
};
if ( true && module && typeof module.exports !== 'undefined') {
  module.exports = SVGtoPDF;
}

/***/ }),

/***/ 65300:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


__webpack_require__(49063);
__webpack_require__(97514);
__webpack_require__(11765);
__webpack_require__(81755);
__webpack_require__(80055);
__webpack_require__(20731);
__webpack_require__(7283);
__webpack_require__(18756);
__webpack_require__(37309);
__webpack_require__(14032);
__webpack_require__(76014);
__webpack_require__(58281);
__webpack_require__(28356);
__webpack_require__(42437);
__webpack_require__(94712);
var TraversalTracker = __webpack_require__(2318);
var DocPreprocessor = __webpack_require__(98883);
var DocMeasure = __webpack_require__(42526);
var DocumentContext = __webpack_require__(79178);
var PageElementWriter = __webpack_require__(11220);
var ColumnCalculator = __webpack_require__(77530);
var TableProcessor = __webpack_require__(89836);
var Line = __webpack_require__(70770);
var isString = (__webpack_require__(91867).isString);
var isArray = (__webpack_require__(91867).isArray);
var isUndefined = (__webpack_require__(91867).isUndefined);
var isNull = (__webpack_require__(91867).isNull);
var pack = (__webpack_require__(91867).pack);
var offsetVector = (__webpack_require__(91867).offsetVector);
var fontStringify = (__webpack_require__(91867).fontStringify);
var getNodeId = (__webpack_require__(91867).getNodeId);
var isFunction = (__webpack_require__(91867).isFunction);
var TextTools = __webpack_require__(11548);
var StyleContextStack = __webpack_require__(76442);
var isNumber = (__webpack_require__(91867).isNumber);
function addAll(target, otherArray) {
  otherArray.forEach(function (item) {
    target.push(item);
  });
}

/**
 * Creates an instance of LayoutBuilder - layout engine which turns document-definition-object
 * into a set of pages, lines, inlines and vectors ready to be rendered into a PDF
 *
 * @param {Object} pageSize - an object defining page width and height
 * @param {Object} pageMargins - an object defining top, left, right and bottom margins
 */
function LayoutBuilder(pageSize, pageMargins, imageMeasure, svgMeasure) {
  this.pageSize = pageSize;
  this.pageMargins = pageMargins;
  this.tracker = new TraversalTracker();
  this.imageMeasure = imageMeasure;
  this.svgMeasure = svgMeasure;
  this.tableLayouts = {};
  this.nestedLevel = 0;
}
LayoutBuilder.prototype.registerTableLayouts = function (tableLayouts) {
  this.tableLayouts = pack(this.tableLayouts, tableLayouts);
};

/**
 * Executes layout engine on document-definition-object and creates an array of pages
 * containing positioned Blocks, Lines and inlines
 *
 * @param {Object} docStructure document-definition-object
 * @param {Object} fontProvider font provider
 * @param {Object} styleDictionary dictionary with style definitions
 * @param {Object} defaultStyle default style definition
 * @return {Array} an array of pages
 */
LayoutBuilder.prototype.layoutDocument = function (docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark, pageBreakBeforeFct) {
  function addPageBreaksIfNecessary(linearNodeList, pages) {
    if (!isFunction(pageBreakBeforeFct)) {
      return false;
    }
    linearNodeList = linearNodeList.filter(function (node) {
      return node.positions.length > 0;
    });
    linearNodeList.forEach(function (node) {
      var nodeInfo = {};
      ['id', 'text', 'ul', 'ol', 'table', 'image', 'qr', 'canvas', 'svg', 'columns', 'headlineLevel', 'style', 'pageBreak', 'pageOrientation', 'width', 'height'].forEach(function (key) {
        if (node[key] !== undefined) {
          nodeInfo[key] = node[key];
        }
      });
      nodeInfo.startPosition = node.positions[0];
      nodeInfo.pageNumbers = Array.from(new Set(node.positions.map(function (node) {
        return node.pageNumber;
      })));
      nodeInfo.pages = pages.length;
      nodeInfo.stack = isArray(node.stack);
      node.nodeInfo = nodeInfo;
    });
    for (var index = 0; index < linearNodeList.length; index++) {
      var node = linearNodeList[index];
      if (node.pageBreak !== 'before' && !node.pageBreakCalculated) {
        node.pageBreakCalculated = true;
        var pageNumber = node.nodeInfo.pageNumbers[0];
        var followingNodesOnPage = [];
        var nodesOnNextPage = [];
        var previousNodesOnPage = [];
        if (pageBreakBeforeFct.length > 1) {
          for (var ii = index + 1, l = linearNodeList.length; ii < l; ii++) {
            if (linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber) > -1) {
              followingNodesOnPage.push(linearNodeList[ii].nodeInfo);
            }
            if (pageBreakBeforeFct.length > 2 && linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber + 1) > -1) {
              nodesOnNextPage.push(linearNodeList[ii].nodeInfo);
            }
          }
        }
        if (pageBreakBeforeFct.length > 3) {
          for (var ii = 0; ii < index; ii++) {
            if (linearNodeList[ii].nodeInfo.pageNumbers.indexOf(pageNumber) > -1) {
              previousNodesOnPage.push(linearNodeList[ii].nodeInfo);
            }
          }
        }
        if (pageBreakBeforeFct(node.nodeInfo, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage)) {
          node.pageBreak = 'before';
          return true;
        }
      }
    }
    return false;
  }
  this.docPreprocessor = new DocPreprocessor();
  this.docMeasure = new DocMeasure(fontProvider, styleDictionary, defaultStyle, this.imageMeasure, this.svgMeasure, this.tableLayouts, images);
  function resetXYs(result) {
    result.linearNodeList.forEach(function (node) {
      node.resetXY();
    });
  }
  var result = this.tryLayoutDocument(docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark);
  while (addPageBreaksIfNecessary(result.linearNodeList, result.pages)) {
    resetXYs(result);
    result = this.tryLayoutDocument(docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark);
  }
  return result.pages;
};
LayoutBuilder.prototype.tryLayoutDocument = function (docStructure, fontProvider, styleDictionary, defaultStyle, background, header, footer, images, watermark, pageBreakBeforeFct) {
  this.linearNodeList = [];
  docStructure = this.docPreprocessor.preprocessDocument(docStructure);
  docStructure = this.docMeasure.measureDocument(docStructure);
  this.writer = new PageElementWriter(new DocumentContext(this.pageSize, this.pageMargins), this.tracker);
  var _this = this;
  this.writer.context().tracker.startTracking('pageAdded', function () {
    _this.addBackground(background);
  });
  this.addBackground(background);
  this.processNode(docStructure);
  this.addHeadersAndFooters(header, footer);
  if (watermark != null) {
    this.addWatermark(watermark, fontProvider, defaultStyle);
  }
  return {
    pages: this.writer.context().pages,
    linearNodeList: this.linearNodeList
  };
};
LayoutBuilder.prototype.addBackground = function (background) {
  var backgroundGetter = isFunction(background) ? background : function () {
    return background;
  };
  var context = this.writer.context();
  var pageSize = context.getCurrentPage().pageSize;
  var pageBackground = backgroundGetter(context.page + 1, pageSize);
  if (pageBackground) {
    this.writer.beginUnbreakableBlock(pageSize.width, pageSize.height);
    pageBackground = this.docPreprocessor.preprocessDocument(pageBackground);
    this.processNode(this.docMeasure.measureDocument(pageBackground));
    this.writer.commitUnbreakableBlock(0, 0);
    context.backgroundLength[context.page] += pageBackground.positions.length;
  }
};
LayoutBuilder.prototype.addStaticRepeatable = function (headerOrFooter, sizeFunction) {
  this.addDynamicRepeatable(function () {
    return JSON.parse(JSON.stringify(headerOrFooter)); // copy to new object
  }, sizeFunction);
};
LayoutBuilder.prototype.addDynamicRepeatable = function (nodeGetter, sizeFunction) {
  var pages = this.writer.context().pages;
  for (var pageIndex = 0, l = pages.length; pageIndex < l; pageIndex++) {
    this.writer.context().page = pageIndex;
    var node = nodeGetter(pageIndex + 1, l, this.writer.context().pages[pageIndex].pageSize);
    if (node) {
      var sizes = sizeFunction(this.writer.context().getCurrentPage().pageSize, this.pageMargins);
      this.writer.beginUnbreakableBlock(sizes.width, sizes.height);
      node = this.docPreprocessor.preprocessDocument(node);
      this.processNode(this.docMeasure.measureDocument(node));
      this.writer.commitUnbreakableBlock(sizes.x, sizes.y);
    }
  }
};
LayoutBuilder.prototype.addHeadersAndFooters = function (header, footer) {
  var headerSizeFct = function headerSizeFct(pageSize, pageMargins) {
    return {
      x: 0,
      y: 0,
      width: pageSize.width,
      height: pageMargins.top
    };
  };
  var footerSizeFct = function footerSizeFct(pageSize, pageMargins) {
    return {
      x: 0,
      y: pageSize.height - pageMargins.bottom,
      width: pageSize.width,
      height: pageMargins.bottom
    };
  };
  if (isFunction(header)) {
    this.addDynamicRepeatable(header, headerSizeFct);
  } else if (header) {
    this.addStaticRepeatable(header, headerSizeFct);
  }
  if (isFunction(footer)) {
    this.addDynamicRepeatable(footer, footerSizeFct);
  } else if (footer) {
    this.addStaticRepeatable(footer, footerSizeFct);
  }
};
LayoutBuilder.prototype.addWatermark = function (watermark, fontProvider, defaultStyle) {
  if (isString(watermark)) {
    watermark = {
      'text': watermark
    };
  }
  if (!watermark.text) {
    // empty watermark text
    return;
  }
  var pages = this.writer.context().pages;
  for (var i = 0, l = pages.length; i < l; i++) {
    pages[i].watermark = getWatermarkObject(Object.assign({}, watermark), pages[i].pageSize, fontProvider, defaultStyle);
  }
  function getWatermarkObject(watermark, pageSize, fontProvider, defaultStyle) {
    watermark.font = watermark.font || defaultStyle.font || 'Roboto';
    watermark.fontSize = watermark.fontSize || 'auto';
    watermark.color = watermark.color || 'black';
    watermark.opacity = isNumber(watermark.opacity) ? watermark.opacity : 0.6;
    watermark.bold = watermark.bold || false;
    watermark.italics = watermark.italics || false;
    watermark.angle = !isUndefined(watermark.angle) && !isNull(watermark.angle) ? watermark.angle : null;
    if (watermark.angle === null) {
      watermark.angle = Math.atan2(pageSize.height, pageSize.width) * -180 / Math.PI;
    }
    if (watermark.fontSize === 'auto') {
      watermark.fontSize = getWatermarkFontSize(pageSize, watermark, fontProvider);
    }
    var watermarkObject = {
      text: watermark.text,
      font: fontProvider.provideFont(watermark.font, watermark.bold, watermark.italics),
      fontSize: watermark.fontSize,
      color: watermark.color,
      opacity: watermark.opacity,
      angle: watermark.angle
    };
    watermarkObject._size = getWatermarkSize(watermark, fontProvider);
    return watermarkObject;
  }
  function getWatermarkSize(watermark, fontProvider) {
    var textTools = new TextTools(fontProvider);
    var styleContextStack = new StyleContextStack(null, {
      font: watermark.font,
      bold: watermark.bold,
      italics: watermark.italics
    });
    styleContextStack.push({
      fontSize: watermark.fontSize
    });
    var size = textTools.sizeOfString(watermark.text, styleContextStack);
    var rotatedSize = textTools.sizeOfRotatedText(watermark.text, watermark.angle, styleContextStack);
    return {
      size: size,
      rotatedSize: rotatedSize
    };
  }
  function getWatermarkFontSize(pageSize, watermark, fontProvider) {
    var textTools = new TextTools(fontProvider);
    var styleContextStack = new StyleContextStack(null, {
      font: watermark.font,
      bold: watermark.bold,
      italics: watermark.italics
    });
    var rotatedSize;

    /**
     * Binary search the best font size.
     * Initial bounds [0, 1000]
     * Break when range < 1
     */
    var a = 0;
    var b = 1000;
    var c = (a + b) / 2;
    while (Math.abs(a - b) > 1) {
      styleContextStack.push({
        fontSize: c
      });
      rotatedSize = textTools.sizeOfRotatedText(watermark.text, watermark.angle, styleContextStack);
      if (rotatedSize.width > pageSize.width) {
        b = c;
        c = (a + b) / 2;
      } else if (rotatedSize.width < pageSize.width) {
        if (rotatedSize.height > pageSize.height) {
          b = c;
          c = (a + b) / 2;
        } else {
          a = c;
          c = (a + b) / 2;
        }
      }
      styleContextStack.pop();
    }
    /*
     End binary search
     */
    return c;
  }
};
function decorateNode(node) {
  var x = node.x,
    y = node.y;
  node.positions = [];
  if (isArray(node.canvas)) {
    node.canvas.forEach(function (vector) {
      var x = vector.x,
        y = vector.y,
        x1 = vector.x1,
        y1 = vector.y1,
        x2 = vector.x2,
        y2 = vector.y2;
      vector.resetXY = function () {
        vector.x = x;
        vector.y = y;
        vector.x1 = x1;
        vector.y1 = y1;
        vector.x2 = x2;
        vector.y2 = y2;
      };
    });
  }
  node.resetXY = function () {
    node.x = x;
    node.y = y;
    if (isArray(node.canvas)) {
      node.canvas.forEach(function (vector) {
        vector.resetXY();
      });
    }
  };
}
LayoutBuilder.prototype.processNode = function (node) {
  var self = this;
  this.linearNodeList.push(node);
  decorateNode(node);
  applyMargins(function () {
    var unbreakable = node.unbreakable;
    if (unbreakable) {
      self.writer.beginUnbreakableBlock();
    }
    var absPosition = node.absolutePosition;
    if (absPosition) {
      self.writer.context().beginDetachedBlock();
      self.writer.context().moveTo(absPosition.x || 0, absPosition.y || 0);
    }
    var relPosition = node.relativePosition;
    if (relPosition) {
      self.writer.context().beginDetachedBlock();
      self.writer.context().moveToRelative(relPosition.x || 0, relPosition.y || 0);
    }
    if (node.stack) {
      self.processVerticalContainer(node);
    } else if (node.columns) {
      self.processColumns(node);
    } else if (node.ul) {
      self.processList(false, node);
    } else if (node.ol) {
      self.processList(true, node);
    } else if (node.table) {
      self.processTable(node);
    } else if (node.text !== undefined) {
      self.processLeaf(node);
    } else if (node.toc) {
      self.processToc(node);
    } else if (node.image) {
      self.processImage(node);
    } else if (node.svg) {
      self.processSVG(node);
    } else if (node.canvas) {
      self.processCanvas(node);
    } else if (node.qr) {
      self.processQr(node);
    } else if (!node._span) {
      throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
    }
    if (absPosition || relPosition) {
      self.writer.context().endDetachedBlock();
    }
    if (unbreakable) {
      self.writer.commitUnbreakableBlock();
    }
  });
  function applyMargins(callback) {
    var margin = node._margin;
    if (node.pageBreak === 'before') {
      self.writer.moveToNextPage(node.pageOrientation);
    } else if (node.pageBreak === 'beforeOdd') {
      self.writer.moveToNextPage(node.pageOrientation);
      if ((self.writer.context().page + 1) % 2 === 1) {
        self.writer.moveToNextPage(node.pageOrientation);
      }
    } else if (node.pageBreak === 'beforeEven') {
      self.writer.moveToNextPage(node.pageOrientation);
      if ((self.writer.context().page + 1) % 2 === 0) {
        self.writer.moveToNextPage(node.pageOrientation);
      }
    }
    var isDetachedBlock = node.relativePosition || node.absolutePosition;

    // Detached nodes have no margins, their position is only determined by 'x' and 'y'
    if (margin && !isDetachedBlock) {
      var availableHeight = self.writer.context().availableHeight;
      // If top margin is bigger than available space, move to next page
      // Necessary for nodes inside tables
      if (availableHeight - margin[1] < 0) {
        // Consume the whole available space
        self.writer.context().moveDown(availableHeight);
        self.writer.moveToNextPage(node.pageOrientation);
        /**
         * TODO - Something to consider:
         * Right now the node starts at the top of next page (after header)
         * Another option would be to apply just the top margin that has not been consumed in the page before
         * It would something like: this.write.context().moveDown(margin[1] - availableHeight)
         */
      } else {
        self.writer.context().moveDown(margin[1]);
      }

      // Apply lateral margins
      self.writer.context().addMargin(margin[0], margin[2]);
    }
    callback();

    // Detached nodes have no margins, their position is only determined by 'x' and 'y'
    if (margin && !isDetachedBlock) {
      var _availableHeight = self.writer.context().availableHeight;
      // If bottom margin is bigger than available space, move to next page
      // Necessary for nodes inside tables
      if (_availableHeight - margin[3] < 0) {
        self.writer.context().moveDown(_availableHeight);
        self.writer.moveToNextPage(node.pageOrientation);
        /**
         * TODO - Something to consider:
         * Right now next node starts at the top of next page (after header)
         * Another option would be to apply the bottom margin that has not been consumed in the next page?
         * It would something like: this.write.context().moveDown(margin[3] - availableHeight)
         */
      } else {
        self.writer.context().moveDown(margin[3]);
      }

      // Apply lateral margins
      self.writer.context().addMargin(-margin[0], -margin[2]);
    }
    if (node.pageBreak === 'after') {
      self.writer.moveToNextPage(node.pageOrientation);
    } else if (node.pageBreak === 'afterOdd') {
      self.writer.moveToNextPage(node.pageOrientation);
      if ((self.writer.context().page + 1) % 2 === 1) {
        self.writer.moveToNextPage(node.pageOrientation);
      }
    } else if (node.pageBreak === 'afterEven') {
      self.writer.moveToNextPage(node.pageOrientation);
      if ((self.writer.context().page + 1) % 2 === 0) {
        self.writer.moveToNextPage(node.pageOrientation);
      }
    }
  }
};

// vertical container
LayoutBuilder.prototype.processVerticalContainer = function (node) {
  var self = this;
  node.stack.forEach(function (item) {
    self.processNode(item);
    addAll(node.positions, item.positions);

    //TODO: paragraph gap
  });
};

// columns
LayoutBuilder.prototype.processColumns = function (columnNode) {
  this.nestedLevel++;
  var columns = columnNode.columns;
  var availableWidth = this.writer.context().availableWidth;
  var gaps = gapArray(columnNode._gap);
  if (gaps) {
    availableWidth -= (gaps.length - 1) * columnNode._gap;
  }
  ColumnCalculator.buildColumnWidths(columns, availableWidth);
  var result = this.processRow({
    marginX: columnNode._margin ? [columnNode._margin[0], columnNode._margin[2]] : [0, 0],
    cells: columns,
    widths: columns,
    gaps: gaps
  });
  addAll(columnNode.positions, result.positions);
  this.nestedLevel--;
  if (this.nestedLevel === 0) {
    this.writer.context().resetMarginXTopParent();
  }
  function gapArray(gap) {
    if (!gap) {
      return null;
    }
    var gaps = [];
    gaps.push(0);
    for (var i = columns.length - 1; i > 0; i--) {
      gaps.push(gap);
    }
    return gaps;
  }
};

/**
 * Searches for a cell in the same row that starts a rowspan and is positioned immediately before the current cell.
 * Alternatively, it finds a cell where the colspan initiating the rowspan extends to the cell just before the current one.
 *
 * @param {Array<object>} arr - An array representing cells in a row.
 * @param {number} i - The index of the current cell to search backward from.
 * @returns {object|null} The starting cell of the rowspan if found; otherwise, `null`.
 */
LayoutBuilder.prototype._findStartingRowSpanCell = function (arr, i) {
  var requiredColspan = 1;
  for (var index = i - 1; index >= 0; index--) {
    if (!arr[index]._span) {
      if (arr[index].rowSpan > 1 && (arr[index].colSpan || 1) === requiredColspan) {
        return arr[index];
      } else {
        return null;
      }
    }
    requiredColspan++;
  }
  return null;
};

/**
 * Retrieves a page break description for a specified page from a list of page breaks.
 *
 * @param {Array<object>} pageBreaks - An array of page break descriptions, each containing `prevPage` properties.
 * @param {number} page - The page number to find the associated page break for.
 * @returns {object|undefined} The page break description object for the specified page if found; otherwise, `undefined`.
 */
LayoutBuilder.prototype._getPageBreak = function (pageBreaks, page) {
  return pageBreaks.find(function (desc) {
    return desc.prevPage === page;
  });
};
LayoutBuilder.prototype._getPageBreakListBySpan = function (tableNode, page, rowIndex) {
  if (!tableNode || !tableNode._breaksBySpan) {
    return null;
  }
  var breaksList = tableNode._breaksBySpan.filter(function (desc) {
    return desc.prevPage === page && rowIndex <= desc.rowIndexOfSpanEnd;
  });
  var y = Number.MAX_VALUE,
    prevY = Number.MIN_VALUE;
  breaksList.forEach(function (b) {
    prevY = Math.max(b.prevY, prevY);
    y = Math.min(b.y, y);
  });
  return {
    prevPage: page,
    prevY: prevY,
    y: y
  };
};
LayoutBuilder.prototype._findSameRowPageBreakByRowSpanData = function (breaksBySpan, page, rowIndex) {
  if (!breaksBySpan) {
    return null;
  }
  return breaksBySpan.find(function (desc) {
    return desc.prevPage === page && rowIndex === desc.rowIndexOfSpanEnd;
  });
};
LayoutBuilder.prototype._updatePageBreaksData = function (pageBreaks, tableNode, rowIndex) {
  var _this2 = this;
  Object.keys(tableNode._bottomByPage).forEach(function (p) {
    var page = Number(p);
    var pageBreak = _this2._getPageBreak(pageBreaks, page);
    if (pageBreak) {
      pageBreak.prevY = Math.max(pageBreak.prevY, tableNode._bottomByPage[page]);
    }
    if (tableNode._breaksBySpan && tableNode._breaksBySpan.length > 0) {
      var breaksBySpanList = tableNode._breaksBySpan.filter(function (pb) {
        return pb.prevPage === page && rowIndex <= pb.rowIndexOfSpanEnd;
      });
      if (breaksBySpanList && breaksBySpanList.length > 0) {
        breaksBySpanList.forEach(function (b) {
          b.prevY = Math.max(b.prevY, tableNode._bottomByPage[page]);
        });
      }
    }
  });
};

/**
 * Resolves the Y-coordinates for a target object by comparing two break points.
 *
 * @param {object} break1 - The first break point with `prevY` and `y` properties.
 * @param {object} break2 - The second break point with `prevY` and `y` properties.
 * @param {object} target - The target object to be updated with resolved Y-coordinates.
 * @property {number} target.prevY - Updated to the maximum `prevY` value between `break1` and `break2`.
 * @property {number} target.y - Updated to the minimum `y` value between `break1` and `break2`.
 */
LayoutBuilder.prototype._resolveBreakY = function (break1, break2, target) {
  target.prevY = Math.max(break1.prevY, break2.prevY);
  target.y = Math.min(break1.y, break2.y);
};
LayoutBuilder.prototype._storePageBreakData = function (data, startsRowSpan, pageBreaks, tableNode) {
  var pageDesc;
  var pageDescBySpan;
  if (!startsRowSpan) {
    pageDesc = this._getPageBreak(pageBreaks, data.prevPage);
    pageDescBySpan = this._getPageBreakListBySpan(tableNode, data.prevPage, data.rowIndex);
    if (!pageDesc) {
      pageDesc = Object.assign({}, data);
      pageBreaks.push(pageDesc);
    }
    if (pageDescBySpan) {
      this._resolveBreakY(pageDesc, pageDescBySpan, pageDesc);
    }
    this._resolveBreakY(pageDesc, data, pageDesc);
  } else {
    var breaksBySpan = tableNode && tableNode._breaksBySpan || null;
    pageDescBySpan = this._findSameRowPageBreakByRowSpanData(breaksBySpan, data.prevPage, data.rowIndex);
    if (!pageDescBySpan) {
      pageDescBySpan = Object.assign({}, data, {
        rowIndexOfSpanEnd: data.rowIndex + data.rowSpan - 1
      });
      if (!tableNode._breaksBySpan) {
        tableNode._breaksBySpan = [];
      }
      tableNode._breaksBySpan.push(pageDescBySpan);
    }
    pageDescBySpan.prevY = Math.max(pageDescBySpan.prevY, data.prevY);
    pageDescBySpan.y = Math.min(pageDescBySpan.y, data.y);
    pageDesc = this._getPageBreak(pageBreaks, data.prevPage);
    if (pageDesc) {
      this._resolveBreakY(pageDesc, pageDescBySpan, pageDesc);
    }
  }
};

/**
 * Calculates the left offset for a column based on the specified gap values.
 *
 * @param {number} i - The index of the column for which the offset is being calculated.
 * @param {Array<number>} gaps - An array of gap values for each column.
 * @returns {number} The left offset for the column. Returns `gaps[i]` if it exists, otherwise `0`.
 */
LayoutBuilder.prototype._colLeftOffset = function (i, gaps) {
  if (gaps && gaps.length > i) {
    return gaps[i];
  }
  return 0;
};

/**
 * Retrieves the ending cell for a row span in case it exists in a specified table column.
 *
 * @param {Array<Array<object>>} tableBody - The table body, represented as a 2D array of cell objects.
 * @param {number} rowIndex - The index of the starting row for the row span.
 * @param {object} column - The column object containing row span information.
 * @param {number} columnIndex - The index of the column within the row.
 * @returns {object|null} The cell at the end of the row span if it exists; otherwise, `null`.
 * @throws {Error} If the row span extends beyond the total row count.
 */
LayoutBuilder.prototype._getRowSpanEndingCell = function (tableBody, rowIndex, column, columnIndex) {
  if (column.rowSpan && column.rowSpan > 1) {
    var endingRow = rowIndex + column.rowSpan - 1;
    if (endingRow >= tableBody.length) {
      throw new Error("Row span for column " + columnIndex + " (with indexes starting from 0) exceeded row count");
    }
    return tableBody[endingRow][columnIndex];
  }
  return null;
};
LayoutBuilder.prototype.processRow = function (_ref) {
  var _ref$marginX = _ref.marginX,
    marginX = _ref$marginX === void 0 ? [0, 0] : _ref$marginX,
    _ref$dontBreakRows = _ref.dontBreakRows,
    dontBreakRows = _ref$dontBreakRows === void 0 ? false : _ref$dontBreakRows,
    _ref$rowsWithoutPageB = _ref.rowsWithoutPageBreak,
    rowsWithoutPageBreak = _ref$rowsWithoutPageB === void 0 ? 0 : _ref$rowsWithoutPageB,
    cells = _ref.cells,
    widths = _ref.widths,
    gaps = _ref.gaps,
    tableNode = _ref.tableNode,
    tableBody = _ref.tableBody,
    rowIndex = _ref.rowIndex,
    height = _ref.height;
  var self = this;
  var isUnbreakableRow = dontBreakRows || rowIndex <= rowsWithoutPageBreak - 1;
  var pageBreaks = [];
  var pageBreaksByRowSpan = [];
  var positions = [];
  var willBreakByHeight = false;
  widths = widths || cells;

  // Check if row should break by height
  if (!isUnbreakableRow && height > self.writer.context().availableHeight) {
    willBreakByHeight = true;
  }

  // Use the marginX if we are in a top level table/column (not nested)
  var marginXParent = self.nestedLevel === 1 ? marginX : null;
  var _bottomByPage = tableNode ? tableNode._bottomByPage : null;
  this.writer.context().beginColumnGroup(marginXParent, _bottomByPage);
  for (var i = 0, l = cells.length; i < l; i++) {
    var cell = cells[i];

    // Page change handler

    this.tracker.auto('pageChanged', storePageBreakClosure, function () {
      var width = widths[i]._calcWidth;
      var leftOffset = self._colLeftOffset(i, gaps);
      // Check if exists and retrieve the cell that started the rowspan in case we are in the cell just after
      var startingSpanCell = self._findStartingRowSpanCell(cells, i);
      if (cell.colSpan && cell.colSpan > 1) {
        for (var j = 1; j < cell.colSpan; j++) {
          width += widths[++i]._calcWidth + gaps[i];
        }
      }

      // if rowspan starts in this cell, we retrieve the last cell affected by the rowspan
      var rowSpanEndingCell = self._getRowSpanEndingCell(tableBody, rowIndex, cell, i);
      if (rowSpanEndingCell) {
        // We store a reference of the ending cell in the first cell of the rowspan
        cell._endingCell = rowSpanEndingCell;
        cell._endingCell._startingRowSpanY = cell._startingRowSpanY;
      }

      // If we are after a cell that started a rowspan
      var endOfRowSpanCell = null;
      if (startingSpanCell && startingSpanCell._endingCell) {
        // Reference to the last cell of the rowspan
        endOfRowSpanCell = startingSpanCell._endingCell;
        // Store if we are in an unbreakable block when we save the context and the originalX
        if (self.writer.transactionLevel > 0) {
          endOfRowSpanCell._isUnbreakableContext = true;
          endOfRowSpanCell._originalXOffset = self.writer.originalX;
        }
      }

      // We pass the endingSpanCell reference to store the context just after processing rowspan cell
      self.writer.context().beginColumn(width, leftOffset, endOfRowSpanCell);
      if (!cell._span) {
        self.processNode(cell);
        self.writer.context().updateBottomByPage();
        addAll(positions, cell.positions);
      } else if (cell._columnEndingContext) {
        var discountY = 0;
        if (dontBreakRows) {
          // Calculate how many points we have to discount to Y when dontBreakRows and rowSpan are combined
          var ctxBeforeRowSpanLastRow = self.writer.writer.contextStack[self.writer.writer.contextStack.length - 1];
          discountY = ctxBeforeRowSpanLastRow.y - cell._startingRowSpanY;
        }
        var originalXOffset = 0;
        // If context was saved from an unbreakable block and we are not in an unbreakable block anymore
        // We have to sum the originalX (X before starting unbreakable block) to X
        if (cell._isUnbreakableContext && !self.writer.transactionLevel) {
          originalXOffset = cell._originalXOffset;
        }
        // row-span ending
        // Recover the context after processing the rowspanned cell
        self.writer.context().markEnding(cell, originalXOffset, discountY);
      }
    });
  }

  // Check if last cell is part of a span
  var endingSpanCell = null;
  var lastColumn = cells.length > 0 ? cells[cells.length - 1] : null;
  if (lastColumn) {
    // Previous column cell has a rowspan
    if (lastColumn._endingCell) {
      endingSpanCell = lastColumn._endingCell;
      // Previous column cell is part of a span
    } else if (lastColumn._span === true) {
      // We get the cell that started the span where we set a reference to the ending cell
      var startingSpanCell = this._findStartingRowSpanCell(cells, cells.length);
      if (startingSpanCell) {
        // Context will be stored here (ending cell)
        endingSpanCell = startingSpanCell._endingCell;
        // Store if we are in an unbreakable block when we save the context and the originalX
        if (this.writer.transactionLevel > 0) {
          endingSpanCell._isUnbreakableContext = true;
          endingSpanCell._originalXOffset = this.writer.originalX;
        }
      }
    }
  }

  // If content did not break page, check if we should break by height
  if (willBreakByHeight && !isUnbreakableRow && pageBreaks.length === 0) {
    this.writer.context().moveDown(this.writer.context().availableHeight);
    this.writer.moveToNextPage();
  }
  var bottomByPage = this.writer.context().completeColumnGroup(height, endingSpanCell);
  if (tableNode) {
    tableNode._bottomByPage = bottomByPage;
    // If there are page breaks in this row, update data with prevY of last cell
    this._updatePageBreaksData(pageBreaks, tableNode, rowIndex);
  }
  return {
    pageBreaksBySpan: pageBreaksByRowSpan,
    pageBreaks: pageBreaks,
    positions: positions
  };
  function storePageBreakClosure(data) {
    var startsRowSpan = cell.rowSpan && cell.rowSpan > 1;
    if (startsRowSpan) {
      data.rowSpan = cell.rowSpan;
    }
    data.rowIndex = rowIndex;
    self._storePageBreakData(data, startsRowSpan, pageBreaks, tableNode);
  }
};

// lists
LayoutBuilder.prototype.processList = function (orderedList, node) {
  var self = this,
    items = orderedList ? node.ol : node.ul,
    gapSize = node._gapSize;
  this.writer.context().addMargin(gapSize.width);
  var nextMarker;
  this.tracker.auto('lineAdded', addMarkerToFirstLeaf, function () {
    items.forEach(function (item) {
      nextMarker = item.listMarker;
      self.processNode(item);
      addAll(node.positions, item.positions);
    });
  });
  this.writer.context().addMargin(-gapSize.width);
  function addMarkerToFirstLeaf(line) {
    // I'm not very happy with the way list processing is implemented
    // (both code and algorithm should be rethinked)
    if (nextMarker) {
      var marker = nextMarker;
      nextMarker = null;
      if (marker.canvas) {
        var vector = marker.canvas[0];
        offsetVector(vector, -marker._minWidth, 0);
        self.writer.addVector(vector);
      } else if (marker._inlines) {
        var markerLine = new Line(self.pageSize.width);
        markerLine.addInline(marker._inlines[0]);
        markerLine.x = -marker._minWidth;
        markerLine.y = line.getAscenderHeight() - markerLine.getAscenderHeight();
        self.writer.addLine(markerLine, true);
      }
    }
  }
};

// tables
LayoutBuilder.prototype.processTable = function (tableNode) {
  var _this3 = this;
  this.nestedLevel++;
  var processor = new TableProcessor(tableNode);
  processor.beginTable(this.writer);
  var rowHeights = tableNode.table.heights;
  for (var i = 0, l = tableNode.table.body.length; i < l; i++) {
    // if dontBreakRows and row starts a rowspan
    // we store the 'y' of the beginning of each rowSpan
    if (processor.dontBreakRows) {
      tableNode.table.body[i].forEach(function (cell) {
        if (cell.rowSpan && cell.rowSpan > 1) {
          cell._startingRowSpanY = _this3.writer.context().y;
        }
      });
    }
    processor.beginRow(i, this.writer);
    var height;
    if (isFunction(rowHeights)) {
      height = rowHeights(i);
    } else if (isArray(rowHeights)) {
      height = rowHeights[i];
    } else {
      height = rowHeights;
    }
    if (height === 'auto') {
      height = undefined;
    }
    var pageBeforeProcessing = this.writer.context().page;
    var result = this.processRow({
      marginX: tableNode._margin ? [tableNode._margin[0], tableNode._margin[2]] : [0, 0],
      dontBreakRows: processor.dontBreakRows,
      rowsWithoutPageBreak: processor.rowsWithoutPageBreak,
      cells: tableNode.table.body[i],
      widths: tableNode.table.widths,
      gaps: tableNode._offsets.offsets,
      tableBody: tableNode.table.body,
      tableNode: tableNode,
      rowIndex: i,
      height: height
    });
    addAll(tableNode.positions, result.positions);
    if (!result.pageBreaks || result.pageBreaks.length === 0) {
      var breaksBySpan = tableNode && tableNode._breaksBySpan || null;
      var breakBySpanData = this._findSameRowPageBreakByRowSpanData(breaksBySpan, pageBeforeProcessing, i);
      if (breakBySpanData) {
        var finalBreakBySpanData = this._getPageBreakListBySpan(tableNode, breakBySpanData.prevPage, i);
        result.pageBreaks.push(finalBreakBySpanData);
      }
    }
    processor.endRow(i, this.writer, result.pageBreaks);
  }
  processor.endTable(this.writer);
  this.nestedLevel--;
  if (this.nestedLevel === 0) {
    this.writer.context().resetMarginXTopParent();
  }
};

// leafs (texts)
LayoutBuilder.prototype.processLeaf = function (node) {
  var line = this.buildNextLine(node);
  if (line && (node.tocItem || node.id)) {
    line._node = node;
  }
  var currentHeight = line ? line.getHeight() : 0;
  var maxHeight = node.maxHeight || -1;
  if (line) {
    var nodeId = getNodeId(node);
    if (nodeId) {
      line.id = nodeId;
    }
  }
  if (node._tocItemRef) {
    line._pageNodeRef = node._tocItemRef;
  }
  if (node._pageRef) {
    line._pageNodeRef = node._pageRef._nodeRef;
  }
  if (line && line.inlines && isArray(line.inlines)) {
    for (var i = 0, l = line.inlines.length; i < l; i++) {
      if (line.inlines[i]._tocItemRef) {
        line.inlines[i]._pageNodeRef = line.inlines[i]._tocItemRef;
      }
      if (line.inlines[i]._pageRef) {
        line.inlines[i]._pageNodeRef = line.inlines[i]._pageRef._nodeRef;
      }
    }
  }
  while (line && (maxHeight === -1 || currentHeight < maxHeight)) {
    var positions = this.writer.addLine(line);
    node.positions.push(positions);
    line = this.buildNextLine(node);
    if (line) {
      currentHeight += line.getHeight();
    }
  }
};
LayoutBuilder.prototype.processToc = function (node) {
  if (node.toc.title) {
    this.processNode(node.toc.title);
  }
  if (node.toc._table) {
    this.processNode(node.toc._table);
  }
};
LayoutBuilder.prototype.buildNextLine = function (textNode) {
  function cloneInline(inline) {
    var newInline = inline.constructor();
    for (var key in inline) {
      newInline[key] = inline[key];
    }
    return newInline;
  }
  if (!textNode._inlines || textNode._inlines.length === 0) {
    return null;
  }
  var line = new Line(this.writer.context().availableWidth);
  var textTools = new TextTools(null);
  var isForceContinue = false;
  while (textNode._inlines && textNode._inlines.length > 0 && (line.hasEnoughSpaceForInline(textNode._inlines[0], textNode._inlines.slice(1)) || isForceContinue)) {
    var isHardWrap = false;
    var inline = textNode._inlines.shift();
    isForceContinue = false;
    if (!inline.noWrap && inline.text.length > 1 && inline.width > line.getAvailableWidth()) {
      var widthPerChar = inline.width / inline.text.length;
      var maxChars = Math.floor(line.getAvailableWidth() / widthPerChar);
      if (maxChars < 1) {
        maxChars = 1;
      }
      if (maxChars < inline.text.length) {
        var newInline = cloneInline(inline);
        newInline.text = inline.text.substr(maxChars);
        inline.text = inline.text.substr(0, maxChars);
        newInline.width = textTools.widthOfString(newInline.text, newInline.font, newInline.fontSize, newInline.characterSpacing, newInline.fontFeatures);
        inline.width = textTools.widthOfString(inline.text, inline.font, inline.fontSize, inline.characterSpacing, inline.fontFeatures);
        textNode._inlines.unshift(newInline);
        isHardWrap = true;
      }
    }
    line.addInline(inline);
    isForceContinue = inline.noNewLine && !isHardWrap;
  }
  line.lastLineInParagraph = textNode._inlines.length === 0;
  return line;
};

// images
LayoutBuilder.prototype.processImage = function (node) {
  var position = this.writer.addImage(node);
  node.positions.push(position);
};
LayoutBuilder.prototype.processSVG = function (node) {
  var position = this.writer.addSVG(node);
  node.positions.push(position);
};
LayoutBuilder.prototype.processCanvas = function (node) {
  var height = node._minHeight;
  if (node.absolutePosition === undefined && this.writer.context().availableHeight < height) {
    // TODO: support for canvas larger than a page
    // TODO: support for other overflow methods

    this.writer.moveToNextPage();
  }
  this.writer.alignCanvas(node);
  node.canvas.forEach(function (vector) {
    var position = this.writer.addVector(vector);
    node.positions.push(position);
  }, this);
  this.writer.context().moveDown(height);
};
LayoutBuilder.prototype.processQr = function (node) {
  var position = this.writer.addQr(node);
  node.positions.push(position);
};
module.exports = LayoutBuilder;

/***/ }),

/***/ 30357:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
var __dirname = "/";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


__webpack_require__(69330);
__webpack_require__(5597);
__webpack_require__(11765);
__webpack_require__(7585);
__webpack_require__(7283);
__webpack_require__(58281);
Object.defineProperty(exports, "__esModule", ({
  value: true
}));
exports["default"] = void 0;
__webpack_require__(65292);
__webpack_require__(73844);
__webpack_require__(39081);
__webpack_require__(41584);
__webpack_require__(49063);
__webpack_require__(58028);
__webpack_require__(81755);
__webpack_require__(94845);
__webpack_require__(80055);
__webpack_require__(20731);
__webpack_require__(45337);
__webpack_require__(8953);
__webpack_require__(24863);
__webpack_require__(70095);
__webpack_require__(10849);
__webpack_require__(18756);
__webpack_require__(37309);
__webpack_require__(14032);
__webpack_require__(59883);
__webpack_require__(68067);
__webpack_require__(61726);
__webpack_require__(57114);
__webpack_require__(26663);
__webpack_require__(65578);
__webpack_require__(6422);
__webpack_require__(46467);
__webpack_require__(7851);
__webpack_require__(72095);
__webpack_require__(62046);
__webpack_require__(47259);
__webpack_require__(18425);
__webpack_require__(56912);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
__webpack_require__(64654);
__webpack_require__(42437);
__webpack_require__(94712);
var _stream = _interopRequireDefault(__webpack_require__(9760));
var _zlib = _interopRequireDefault(__webpack_require__(6729));
var _cryptoJs = _interopRequireDefault(__webpack_require__(94119));
var _fontkit = _interopRequireDefault(__webpack_require__(10740));
var _events = __webpack_require__(64785);
var _linebreak = _interopRequireDefault(__webpack_require__(5417));
var _jpegExif = _interopRequireDefault(__webpack_require__(38834));
var _pngJs = _interopRequireDefault(__webpack_require__(84695));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _readOnlyError(r) { throw new TypeError('"' + r + '" is read-only'); }
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
function _createForOfIteratorHelperLoose(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (t) return (t = t.call(r)).next.bind(t); if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var o = 0; return function () { return o >= r.length ? { done: !0 } : { done: !1, value: r[o++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
var fs = __webpack_require__(48181);
/*
PDFAbstractReference - abstract class for PDF reference
*/
var PDFAbstractReference = /*#__PURE__*/function () {
  function PDFAbstractReference() {}
  var _proto = PDFAbstractReference.prototype;
  _proto.toString = function toString() {
    throw new Error('Must be implemented by subclasses');
  };
  return PDFAbstractReference;
}();
/*
PDFTree - abstract base class for name and number tree objects
*/
var PDFTree = /*#__PURE__*/function () {
  function PDFTree() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    this._items = {};
    // disable /Limits output for this tree
    this.limits = typeof options.limits === 'boolean' ? options.limits : true;
  }
  var _proto2 = PDFTree.prototype;
  _proto2.add = function add(key, val) {
    return this._items[key] = val;
  };
  _proto2.get = function get(key) {
    return this._items[key];
  };
  _proto2.toString = function toString() {
    var _this = this;
    // Needs to be sorted by key
    var sortedKeys = Object.keys(this._items).sort(function (a, b) {
      return _this._compareKeys(a, b);
    });
    var out = ['<<'];
    if (this.limits && sortedKeys.length > 1) {
      var _first = sortedKeys[0],
        _last = sortedKeys[sortedKeys.length - 1];
      out.push("  /Limits " + PDFObject.convert([this._dataForKey(_first), this._dataForKey(_last)]));
    }
    out.push("  /" + this._keysName() + " [");
    for (var _iterator = _createForOfIteratorHelperLoose(sortedKeys), _step; !(_step = _iterator()).done;) {
      var key = _step.value;
      out.push("    " + PDFObject.convert(this._dataForKey(key)) + " " + PDFObject.convert(this._items[key]));
    }
    out.push(']');
    out.push('>>');
    return out.join('\n');
  };
  _proto2._compareKeys = function _compareKeys(/*a, b*/
  ) {
    throw new Error('Must be implemented by subclasses');
  };
  _proto2._keysName = function _keysName() {
    throw new Error('Must be implemented by subclasses');
  };
  _proto2._dataForKey = function _dataForKey(/*k*/
  ) {
    throw new Error('Must be implemented by subclasses');
  };
  return PDFTree;
}();
var SpotColor = /*#__PURE__*/function () {
  function SpotColor(doc, name, C, M, Y, K) {
    this.id = 'CS' + Object.keys(doc.spotColors).length;
    this.name = name;
    this.values = [C, M, Y, K];
    this.ref = doc.ref(['Separation', this.name, 'DeviceCMYK', {
      Range: [0, 1, 0, 1, 0, 1, 0, 1],
      C0: [0, 0, 0, 0],
      C1: this.values.map(function (value) {
        return value / 100;
      }),
      FunctionType: 2,
      Domain: [0, 1],
      N: 1
    }]);
    this.ref.end();
  }
  var _proto3 = SpotColor.prototype;
  _proto3.toString = function toString() {
    return this.ref.id + " 0 R";
  };
  return SpotColor;
}();
/*
PDFObject - converts JavaScript types into their corresponding PDF types.
By Devon Govett
*/
var pad = function pad(str, length) {
  return (Array(length + 1).join('0') + str).slice(-length);
};
var escapableRe = /[\n\r\t\b\f()\\]/g;
var escapable = {
  '\n': '\\n',
  '\r': '\\r',
  '\t': '\\t',
  '\b': '\\b',
  '\f': '\\f',
  '\\': '\\\\',
  '(': '\\(',
  ')': '\\)'
};

// Convert little endian UTF-16 to big endian
var swapBytes = function swapBytes(buff) {
  var l = buff.length;
  if (l & 0x01) {
    throw new Error('Buffer length must be even');
  } else {
    for (var i = 0, end = l - 1; i < end; i += 2) {
      var a = buff[i];
      buff[i] = buff[i + 1];
      buff[i + 1] = a;
    }
  }
  return buff;
};
var PDFObject = /*#__PURE__*/function () {
  function PDFObject() {}
  PDFObject.convert = function convert(object) {
    var encryptFn = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    // String literals are converted to the PDF name type
    if (typeof object === 'string') {
      return "/" + object;

      // String objects are converted to PDF strings (UTF-16)
    } else if (object instanceof String) {
      var string = object;
      // Detect if this is a unicode string
      var isUnicode = false;
      for (var i = 0, end = string.length; i < end; i++) {
        if (string.charCodeAt(i) > 0x7f) {
          isUnicode = true;
          break;
        }
      }

      // If so, encode it as big endian UTF-16
      var stringBuffer;
      if (isUnicode) {
        stringBuffer = swapBytes(Buffer.from("\uFEFF" + string, 'utf16le'));
      } else {
        stringBuffer = Buffer.from(string.valueOf(), 'ascii');
      }

      // Encrypt the string when necessary
      if (encryptFn) {
        string = encryptFn(stringBuffer).toString('binary');
      } else {
        string = stringBuffer.toString('binary');
      }

      // Escape characters as required by the spec
      string = string.replace(escapableRe, function (c) {
        return escapable[c];
      });
      return "(" + string + ")";

      // Buffers are converted to PDF hex strings
    } else if (Buffer.isBuffer(object)) {
      return "<" + object.toString('hex') + ">";
    } else if (object instanceof PDFAbstractReference || object instanceof PDFTree || object instanceof SpotColor) {
      return object.toString();
    } else if (object instanceof Date) {
      var _string = "D:" + pad(object.getUTCFullYear(), 4) + pad(object.getUTCMonth() + 1, 2) + pad(object.getUTCDate(), 2) + pad(object.getUTCHours(), 2) + pad(object.getUTCMinutes(), 2) + pad(object.getUTCSeconds(), 2) + 'Z';

      // Encrypt the string when necessary
      if (encryptFn) {
        _string = encryptFn(Buffer.from(_string, 'ascii')).toString('binary');

        // Escape characters as required by the spec
        _string = _string.replace(escapableRe, function (c) {
          return escapable[c];
        });
      }
      return "(" + _string + ")";
    } else if (Array.isArray(object)) {
      var items = object.map(function (e) {
        return PDFObject.convert(e, encryptFn);
      }).join(' ');
      return "[" + items + "]";
    } else if ({}.toString.call(object) === '[object Object]') {
      var out = ['<<'];
      for (var key in object) {
        var val = object[key];
        out.push("/" + key + " " + PDFObject.convert(val, encryptFn));
      }
      out.push('>>');
      return out.join('\n');
    } else if (typeof object === 'number') {
      return PDFObject.number(object);
    } else {
      return "" + object;
    }
  };
  PDFObject.number = function number(n) {
    if (n > -1e21 && n < 1e21) {
      return Math.round(n * 1e6) / 1e6;
    }
    throw new Error("unsupported number: " + n);
  };
  return PDFObject;
}();
/*
PDFReference - represents a reference to another object in the PDF object heirarchy
By Devon Govett
*/
var PDFReference = /*#__PURE__*/function (_PDFAbstractReference) {
  function PDFReference(document, id) {
    var _this2;
    var data = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    _this2 = _PDFAbstractReference.call(this) || this;
    _this2.document = document;
    _this2.id = id;
    _this2.data = data;
    _this2.gen = 0;
    _this2.compress = _this2.document.compress && !_this2.data.Filter;
    _this2.uncompressedLength = 0;
    _this2.buffer = [];
    return _this2;
  }
  _inheritsLoose(PDFReference, _PDFAbstractReference);
  var _proto4 = PDFReference.prototype;
  _proto4.write = function write(chunk) {
    if (!Buffer.isBuffer(chunk)) {
      chunk = Buffer.from(chunk + '\n', 'binary');
    }
    this.uncompressedLength += chunk.length;
    if (this.data.Length == null) {
      this.data.Length = 0;
    }
    this.buffer.push(chunk);
    this.data.Length += chunk.length;
    if (this.compress) {
      return this.data.Filter = 'FlateDecode';
    }
  };
  _proto4.end = function end(chunk) {
    if (chunk) {
      this.write(chunk);
    }
    return this.finalize();
  };
  _proto4.finalize = function finalize() {
    this.offset = this.document._offset;
    var encryptFn = this.document._security ? this.document._security.getEncryptFn(this.id, this.gen) : null;
    if (this.buffer.length) {
      this.buffer = Buffer.concat(this.buffer);
      if (this.compress) {
        this.buffer = _zlib.default.deflateSync(this.buffer);
      }
      if (encryptFn) {
        this.buffer = encryptFn(this.buffer);
      }
      this.data.Length = this.buffer.length;
    }
    this.document._write(this.id + " " + this.gen + " obj");
    this.document._write(PDFObject.convert(this.data, encryptFn));
    if (this.buffer.length) {
      this.document._write('stream');
      this.document._write(this.buffer);
      this.buffer = []; // free up memory
      this.document._write('\nendstream');
    }
    this.document._write('endobj');
    this.document._refEnd(this);
  };
  _proto4.toString = function toString() {
    return this.id + " " + this.gen + " R";
  };
  return PDFReference;
}(PDFAbstractReference);
/*
PDFPage - represents a single page in the PDF document
By Devon Govett
*/
var DEFAULT_MARGINS = {
  top: 72,
  left: 72,
  bottom: 72,
  right: 72
};
var SIZES = {
  '4A0': [4767.87, 6740.79],
  '2A0': [3370.39, 4767.87],
  A0: [2383.94, 3370.39],
  A1: [1683.78, 2383.94],
  A2: [1190.55, 1683.78],
  A3: [841.89, 1190.55],
  A4: [595.28, 841.89],
  A5: [419.53, 595.28],
  A6: [297.64, 419.53],
  A7: [209.76, 297.64],
  A8: [147.4, 209.76],
  A9: [104.88, 147.4],
  A10: [73.7, 104.88],
  B0: [2834.65, 4008.19],
  B1: [2004.09, 2834.65],
  B2: [1417.32, 2004.09],
  B3: [1000.63, 1417.32],
  B4: [708.66, 1000.63],
  B5: [498.9, 708.66],
  B6: [354.33, 498.9],
  B7: [249.45, 354.33],
  B8: [175.75, 249.45],
  B9: [124.72, 175.75],
  B10: [87.87, 124.72],
  C0: [2599.37, 3676.54],
  C1: [1836.85, 2599.37],
  C2: [1298.27, 1836.85],
  C3: [918.43, 1298.27],
  C4: [649.13, 918.43],
  C5: [459.21, 649.13],
  C6: [323.15, 459.21],
  C7: [229.61, 323.15],
  C8: [161.57, 229.61],
  C9: [113.39, 161.57],
  C10: [79.37, 113.39],
  RA0: [2437.8, 3458.27],
  RA1: [1729.13, 2437.8],
  RA2: [1218.9, 1729.13],
  RA3: [864.57, 1218.9],
  RA4: [609.45, 864.57],
  SRA0: [2551.18, 3628.35],
  SRA1: [1814.17, 2551.18],
  SRA2: [1275.59, 1814.17],
  SRA3: [907.09, 1275.59],
  SRA4: [637.8, 907.09],
  EXECUTIVE: [521.86, 756.0],
  FOLIO: [612.0, 936.0],
  LEGAL: [612.0, 1008.0],
  LETTER: [612.0, 792.0],
  TABLOID: [792.0, 1224.0]
};
var PDFPage = /*#__PURE__*/function () {
  function PDFPage(document) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    this.document = document;
    this.size = options.size || 'letter';
    this.layout = options.layout || 'portrait';

    // process margins
    if (typeof options.margin === 'number') {
      this.margins = {
        top: options.margin,
        left: options.margin,
        bottom: options.margin,
        right: options.margin
      };

      // default to 1 inch margins
    } else {
      this.margins = options.margins || DEFAULT_MARGINS;
    }

    // calculate page dimensions
    var dimensions = Array.isArray(this.size) ? this.size : SIZES[this.size.toUpperCase()];
    this.width = dimensions[this.layout === 'portrait' ? 0 : 1];
    this.height = dimensions[this.layout === 'portrait' ? 1 : 0];
    this.content = this.document.ref();

    // Initialize the Font, XObject, and ExtGState dictionaries
    this.resources = this.document.ref({
      ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI']
    });

    // The page dictionary
    this.dictionary = this.document.ref({
      Type: 'Page',
      Parent: this.document._root.data.Pages,
      MediaBox: [0, 0, this.width, this.height],
      Contents: this.content,
      Resources: this.resources
    });
    this.markings = [];
  }

  // Lazily create these objects
  var _proto5 = PDFPage.prototype;
  _proto5.maxY = function maxY() {
    return this.height - this.margins.bottom;
  };
  _proto5.write = function write(chunk) {
    return this.content.write(chunk);
  }

  // Set tab order if document is tagged for accessibility.
  ;
  _proto5._setTabOrder = function _setTabOrder() {
    if (!this.dictionary.Tabs && this.document.hasMarkInfoDictionary()) {
      this.dictionary.data.Tabs = 'S';
    }
  };
  _proto5.end = function end() {
    this._setTabOrder();
    this.dictionary.end();
    this.resources.data.ColorSpace = this.resources.data.ColorSpace || {};
    for (var _i = 0, _Object$values = Object.values(this.document.spotColors); _i < _Object$values.length; _i++) {
      var color = _Object$values[_i];
      this.resources.data.ColorSpace[color.id] = color;
    }
    this.resources.end();
    return this.content.end();
  };
  return _createClass(PDFPage, [{
    key: "fonts",
    get: function get() {
      var data = this.resources.data;
      return data.Font != null ? data.Font : data.Font = {};
    }
  }, {
    key: "xobjects",
    get: function get() {
      var data = this.resources.data;
      return data.XObject != null ? data.XObject : data.XObject = {};
    }
  }, {
    key: "ext_gstates",
    get: function get() {
      var data = this.resources.data;
      return data.ExtGState != null ? data.ExtGState : data.ExtGState = {};
    }
  }, {
    key: "patterns",
    get: function get() {
      var data = this.resources.data;
      return data.Pattern != null ? data.Pattern : data.Pattern = {};
    }
  }, {
    key: "colorSpaces",
    get: function get() {
      var data = this.resources.data;
      return data.ColorSpace || (data.ColorSpace = {});
    }
  }, {
    key: "annotations",
    get: function get() {
      var data = this.dictionary.data;
      return data.Annots != null ? data.Annots : data.Annots = [];
    }
  }, {
    key: "structParentTreeKey",
    get: function get() {
      var data = this.dictionary.data;
      return data.StructParents != null ? data.StructParents : data.StructParents = this.document.createStructParentTreeNextKey();
    }
  }]);
}();
/*
PDFNameTree - represents a name tree object
*/
var PDFNameTree = /*#__PURE__*/function (_PDFTree) {
  function PDFNameTree() {
    return _PDFTree.apply(this, arguments) || this;
  }
  _inheritsLoose(PDFNameTree, _PDFTree);
  var _proto6 = PDFNameTree.prototype;
  _proto6._compareKeys = function _compareKeys(a, b) {
    return a.localeCompare(b);
  };
  _proto6._keysName = function _keysName() {
    return "Names";
  };
  _proto6._dataForKey = function _dataForKey(k) {
    return new String(k);
  };
  return PDFNameTree;
}(PDFTree);
/**
 * Check if value is in a range group.
 * @param {number} value
 * @param {number[]} rangeGroup
 * @returns {boolean}
 */
function inRange(value, rangeGroup) {
  if (value < rangeGroup[0]) return false;
  var startRange = 0;
  var endRange = rangeGroup.length / 2;
  while (startRange <= endRange) {
    var middleRange = Math.floor((startRange + endRange) / 2);

    // actual array index
    var arrayIndex = middleRange * 2;

    // Check if value is in range pointed by actual index
    if (value >= rangeGroup[arrayIndex] && value <= rangeGroup[arrayIndex + 1]) {
      return true;
    }
    if (value > rangeGroup[arrayIndex + 1]) {
      // Search Right Side Of Array
      startRange = middleRange + 1;
    } else {
      // Search Left Side Of Array
      endRange = middleRange - 1;
    }
  }
  return false;
}

// prettier-ignore-start
/**
 * A.1 Unassigned code points in Unicode 3.2
 * @link https://tools.ietf.org/html/rfc3454#appendix-A.1
 */
var unassigned_code_points = [0x0221, 0x0221, 0x0234, 0x024f, 0x02ae, 0x02af, 0x02ef, 0x02ff, 0x0350, 0x035f, 0x0370, 0x0373, 0x0376, 0x0379, 0x037b, 0x037d, 0x037f, 0x0383, 0x038b, 0x038b, 0x038d, 0x038d, 0x03a2, 0x03a2, 0x03cf, 0x03cf, 0x03f7, 0x03ff, 0x0487, 0x0487, 0x04cf, 0x04cf, 0x04f6, 0x04f7, 0x04fa, 0x04ff, 0x0510, 0x0530, 0x0557, 0x0558, 0x0560, 0x0560, 0x0588, 0x0588, 0x058b, 0x0590, 0x05a2, 0x05a2, 0x05ba, 0x05ba, 0x05c5, 0x05cf, 0x05eb, 0x05ef, 0x05f5, 0x060b, 0x060d, 0x061a, 0x061c, 0x061e, 0x0620, 0x0620, 0x063b, 0x063f, 0x0656, 0x065f, 0x06ee, 0x06ef, 0x06ff, 0x06ff, 0x070e, 0x070e, 0x072d, 0x072f, 0x074b, 0x077f, 0x07b2, 0x0900, 0x0904, 0x0904, 0x093a, 0x093b, 0x094e, 0x094f, 0x0955, 0x0957, 0x0971, 0x0980, 0x0984, 0x0984, 0x098d, 0x098e, 0x0991, 0x0992, 0x09a9, 0x09a9, 0x09b1, 0x09b1, 0x09b3, 0x09b5, 0x09ba, 0x09bb, 0x09bd, 0x09bd, 0x09c5, 0x09c6, 0x09c9, 0x09ca, 0x09ce, 0x09d6, 0x09d8, 0x09db, 0x09de, 0x09de, 0x09e4, 0x09e5, 0x09fb, 0x0a01, 0x0a03, 0x0a04, 0x0a0b, 0x0a0e, 0x0a11, 0x0a12, 0x0a29, 0x0a29, 0x0a31, 0x0a31, 0x0a34, 0x0a34, 0x0a37, 0x0a37, 0x0a3a, 0x0a3b, 0x0a3d, 0x0a3d, 0x0a43, 0x0a46, 0x0a49, 0x0a4a, 0x0a4e, 0x0a58, 0x0a5d, 0x0a5d, 0x0a5f, 0x0a65, 0x0a75, 0x0a80, 0x0a84, 0x0a84, 0x0a8c, 0x0a8c, 0x0a8e, 0x0a8e, 0x0a92, 0x0a92, 0x0aa9, 0x0aa9, 0x0ab1, 0x0ab1, 0x0ab4, 0x0ab4, 0x0aba, 0x0abb, 0x0ac6, 0x0ac6, 0x0aca, 0x0aca, 0x0ace, 0x0acf, 0x0ad1, 0x0adf, 0x0ae1, 0x0ae5, 0x0af0, 0x0b00, 0x0b04, 0x0b04, 0x0b0d, 0x0b0e, 0x0b11, 0x0b12, 0x0b29, 0x0b29, 0x0b31, 0x0b31, 0x0b34, 0x0b35, 0x0b3a, 0x0b3b, 0x0b44, 0x0b46, 0x0b49, 0x0b4a, 0x0b4e, 0x0b55, 0x0b58, 0x0b5b, 0x0b5e, 0x0b5e, 0x0b62, 0x0b65, 0x0b71, 0x0b81, 0x0b84, 0x0b84, 0x0b8b, 0x0b8d, 0x0b91, 0x0b91, 0x0b96, 0x0b98, 0x0b9b, 0x0b9b, 0x0b9d, 0x0b9d, 0x0ba0, 0x0ba2, 0x0ba5, 0x0ba7, 0x0bab, 0x0bad, 0x0bb6, 0x0bb6, 0x0bba, 0x0bbd, 0x0bc3, 0x0bc5, 0x0bc9, 0x0bc9, 0x0bce, 0x0bd6, 0x0bd8, 0x0be6, 0x0bf3, 0x0c00, 0x0c04, 0x0c04, 0x0c0d, 0x0c0d, 0x0c11, 0x0c11, 0x0c29, 0x0c29, 0x0c34, 0x0c34, 0x0c3a, 0x0c3d, 0x0c45, 0x0c45, 0x0c49, 0x0c49, 0x0c4e, 0x0c54, 0x0c57, 0x0c5f, 0x0c62, 0x0c65, 0x0c70, 0x0c81, 0x0c84, 0x0c84, 0x0c8d, 0x0c8d, 0x0c91, 0x0c91, 0x0ca9, 0x0ca9, 0x0cb4, 0x0cb4, 0x0cba, 0x0cbd, 0x0cc5, 0x0cc5, 0x0cc9, 0x0cc9, 0x0cce, 0x0cd4, 0x0cd7, 0x0cdd, 0x0cdf, 0x0cdf, 0x0ce2, 0x0ce5, 0x0cf0, 0x0d01, 0x0d04, 0x0d04, 0x0d0d, 0x0d0d, 0x0d11, 0x0d11, 0x0d29, 0x0d29, 0x0d3a, 0x0d3d, 0x0d44, 0x0d45, 0x0d49, 0x0d49, 0x0d4e, 0x0d56, 0x0d58, 0x0d5f, 0x0d62, 0x0d65, 0x0d70, 0x0d81, 0x0d84, 0x0d84, 0x0d97, 0x0d99, 0x0db2, 0x0db2, 0x0dbc, 0x0dbc, 0x0dbe, 0x0dbf, 0x0dc7, 0x0dc9, 0x0dcb, 0x0dce, 0x0dd5, 0x0dd5, 0x0dd7, 0x0dd7, 0x0de0, 0x0df1, 0x0df5, 0x0e00, 0x0e3b, 0x0e3e, 0x0e5c, 0x0e80, 0x0e83, 0x0e83, 0x0e85, 0x0e86, 0x0e89, 0x0e89, 0x0e8b, 0x0e8c, 0x0e8e, 0x0e93, 0x0e98, 0x0e98, 0x0ea0, 0x0ea0, 0x0ea4, 0x0ea4, 0x0ea6, 0x0ea6, 0x0ea8, 0x0ea9, 0x0eac, 0x0eac, 0x0eba, 0x0eba, 0x0ebe, 0x0ebf, 0x0ec5, 0x0ec5, 0x0ec7, 0x0ec7, 0x0ece, 0x0ecf, 0x0eda, 0x0edb, 0x0ede, 0x0eff, 0x0f48, 0x0f48, 0x0f6b, 0x0f70, 0x0f8c, 0x0f8f, 0x0f98, 0x0f98, 0x0fbd, 0x0fbd, 0x0fcd, 0x0fce, 0x0fd0, 0x0fff, 0x1022, 0x1022, 0x1028, 0x1028, 0x102b, 0x102b, 0x1033, 0x1035, 0x103a, 0x103f, 0x105a, 0x109f, 0x10c6, 0x10cf, 0x10f9, 0x10fa, 0x10fc, 0x10ff, 0x115a, 0x115e, 0x11a3, 0x11a7, 0x11fa, 0x11ff, 0x1207, 0x1207, 0x1247, 0x1247, 0x1249, 0x1249, 0x124e, 0x124f, 0x1257, 0x1257, 0x1259, 0x1259, 0x125e, 0x125f, 0x1287, 0x1287, 0x1289, 0x1289, 0x128e, 0x128f, 0x12af, 0x12af, 0x12b1, 0x12b1, 0x12b6, 0x12b7, 0x12bf, 0x12bf, 0x12c1, 0x12c1, 0x12c6, 0x12c7, 0x12cf, 0x12cf, 0x12d7, 0x12d7, 0x12ef, 0x12ef, 0x130f, 0x130f, 0x1311, 0x1311, 0x1316, 0x1317, 0x131f, 0x131f, 0x1347, 0x1347, 0x135b, 0x1360, 0x137d, 0x139f, 0x13f5, 0x1400, 0x1677, 0x167f, 0x169d, 0x169f, 0x16f1, 0x16ff, 0x170d, 0x170d, 0x1715, 0x171f, 0x1737, 0x173f, 0x1754, 0x175f, 0x176d, 0x176d, 0x1771, 0x1771, 0x1774, 0x177f, 0x17dd, 0x17df, 0x17ea, 0x17ff, 0x180f, 0x180f, 0x181a, 0x181f, 0x1878, 0x187f, 0x18aa, 0x1dff, 0x1e9c, 0x1e9f, 0x1efa, 0x1eff, 0x1f16, 0x1f17, 0x1f1e, 0x1f1f, 0x1f46, 0x1f47, 0x1f4e, 0x1f4f, 0x1f58, 0x1f58, 0x1f5a, 0x1f5a, 0x1f5c, 0x1f5c, 0x1f5e, 0x1f5e, 0x1f7e, 0x1f7f, 0x1fb5, 0x1fb5, 0x1fc5, 0x1fc5, 0x1fd4, 0x1fd5, 0x1fdc, 0x1fdc, 0x1ff0, 0x1ff1, 0x1ff5, 0x1ff5, 0x1fff, 0x1fff, 0x2053, 0x2056, 0x2058, 0x205e, 0x2064, 0x2069, 0x2072, 0x2073, 0x208f, 0x209f, 0x20b2, 0x20cf, 0x20eb, 0x20ff, 0x213b, 0x213c, 0x214c, 0x2152, 0x2184, 0x218f, 0x23cf, 0x23ff, 0x2427, 0x243f, 0x244b, 0x245f, 0x24ff, 0x24ff, 0x2614, 0x2615, 0x2618, 0x2618, 0x267e, 0x267f, 0x268a, 0x2700, 0x2705, 0x2705, 0x270a, 0x270b, 0x2728, 0x2728, 0x274c, 0x274c, 0x274e, 0x274e, 0x2753, 0x2755, 0x2757, 0x2757, 0x275f, 0x2760, 0x2795, 0x2797, 0x27b0, 0x27b0, 0x27bf, 0x27cf, 0x27ec, 0x27ef, 0x2b00, 0x2e7f, 0x2e9a, 0x2e9a, 0x2ef4, 0x2eff, 0x2fd6, 0x2fef, 0x2ffc, 0x2fff, 0x3040, 0x3040, 0x3097, 0x3098, 0x3100, 0x3104, 0x312d, 0x3130, 0x318f, 0x318f, 0x31b8, 0x31ef, 0x321d, 0x321f, 0x3244, 0x3250, 0x327c, 0x327e, 0x32cc, 0x32cf, 0x32ff, 0x32ff, 0x3377, 0x337a, 0x33de, 0x33df, 0x33ff, 0x33ff, 0x4db6, 0x4dff, 0x9fa6, 0x9fff, 0xa48d, 0xa48f, 0xa4c7, 0xabff, 0xd7a4, 0xd7ff, 0xfa2e, 0xfa2f, 0xfa6b, 0xfaff, 0xfb07, 0xfb12, 0xfb18, 0xfb1c, 0xfb37, 0xfb37, 0xfb3d, 0xfb3d, 0xfb3f, 0xfb3f, 0xfb42, 0xfb42, 0xfb45, 0xfb45, 0xfbb2, 0xfbd2, 0xfd40, 0xfd4f, 0xfd90, 0xfd91, 0xfdc8, 0xfdcf, 0xfdfd, 0xfdff, 0xfe10, 0xfe1f, 0xfe24, 0xfe2f, 0xfe47, 0xfe48, 0xfe53, 0xfe53, 0xfe67, 0xfe67, 0xfe6c, 0xfe6f, 0xfe75, 0xfe75, 0xfefd, 0xfefe, 0xff00, 0xff00, 0xffbf, 0xffc1, 0xffc8, 0xffc9, 0xffd0, 0xffd1, 0xffd8, 0xffd9, 0xffdd, 0xffdf, 0xffe7, 0xffe7, 0xffef, 0xfff8, 0x10000, 0x102ff, 0x1031f, 0x1031f, 0x10324, 0x1032f, 0x1034b, 0x103ff, 0x10426, 0x10427, 0x1044e, 0x1cfff, 0x1d0f6, 0x1d0ff, 0x1d127, 0x1d129, 0x1d1de, 0x1d3ff, 0x1d455, 0x1d455, 0x1d49d, 0x1d49d, 0x1d4a0, 0x1d4a1, 0x1d4a3, 0x1d4a4, 0x1d4a7, 0x1d4a8, 0x1d4ad, 0x1d4ad, 0x1d4ba, 0x1d4ba, 0x1d4bc, 0x1d4bc, 0x1d4c1, 0x1d4c1, 0x1d4c4, 0x1d4c4, 0x1d506, 0x1d506, 0x1d50b, 0x1d50c, 0x1d515, 0x1d515, 0x1d51d, 0x1d51d, 0x1d53a, 0x1d53a, 0x1d53f, 0x1d53f, 0x1d545, 0x1d545, 0x1d547, 0x1d549, 0x1d551, 0x1d551, 0x1d6a4, 0x1d6a7, 0x1d7ca, 0x1d7cd, 0x1d800, 0x1fffd, 0x2a6d7, 0x2f7ff, 0x2fa1e, 0x2fffd, 0x30000, 0x3fffd, 0x40000, 0x4fffd, 0x50000, 0x5fffd, 0x60000, 0x6fffd, 0x70000, 0x7fffd, 0x80000, 0x8fffd, 0x90000, 0x9fffd, 0xa0000, 0xafffd, 0xb0000, 0xbfffd, 0xc0000, 0xcfffd, 0xd0000, 0xdfffd, 0xe0000, 0xe0000, 0xe0002, 0xe001f, 0xe0080, 0xefffd];
// prettier-ignore-end

var isUnassignedCodePoint = function isUnassignedCodePoint(character) {
  return inRange(character, unassigned_code_points);
};

// prettier-ignore-start
/**
 * B.1 Commonly mapped to nothing
 * @link https://tools.ietf.org/html/rfc3454#appendix-B.1
 */
var commonly_mapped_to_nothing = [0x00ad, 0x00ad, 0x034f, 0x034f, 0x1806, 0x1806, 0x180b, 0x180b, 0x180c, 0x180c, 0x180d, 0x180d, 0x200b, 0x200b, 0x200c, 0x200c, 0x200d, 0x200d, 0x2060, 0x2060, 0xfe00, 0xfe00, 0xfe01, 0xfe01, 0xfe02, 0xfe02, 0xfe03, 0xfe03, 0xfe04, 0xfe04, 0xfe05, 0xfe05, 0xfe06, 0xfe06, 0xfe07, 0xfe07, 0xfe08, 0xfe08, 0xfe09, 0xfe09, 0xfe0a, 0xfe0a, 0xfe0b, 0xfe0b, 0xfe0c, 0xfe0c, 0xfe0d, 0xfe0d, 0xfe0e, 0xfe0e, 0xfe0f, 0xfe0f, 0xfeff, 0xfeff];
// prettier-ignore-end

var isCommonlyMappedToNothing = function isCommonlyMappedToNothing(character) {
  return inRange(character, commonly_mapped_to_nothing);
};

// prettier-ignore-start
/**
 * C.1.2 Non-ASCII space characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.1.2
 */
var non_ASCII_space_characters = [0x00a0, 0x00a0 /* NO-BREAK SPACE */, 0x1680, 0x1680 /* OGHAM SPACE MARK */, 0x2000, 0x2000 /* EN QUAD */, 0x2001, 0x2001 /* EM QUAD */, 0x2002, 0x2002 /* EN SPACE */, 0x2003, 0x2003 /* EM SPACE */, 0x2004, 0x2004 /* THREE-PER-EM SPACE */, 0x2005, 0x2005 /* FOUR-PER-EM SPACE */, 0x2006, 0x2006 /* SIX-PER-EM SPACE */, 0x2007, 0x2007 /* FIGURE SPACE */, 0x2008, 0x2008 /* PUNCTUATION SPACE */, 0x2009, 0x2009 /* THIN SPACE */, 0x200a, 0x200a /* HAIR SPACE */, 0x200b, 0x200b /* ZERO WIDTH SPACE */, 0x202f, 0x202f /* NARROW NO-BREAK SPACE */, 0x205f, 0x205f /* MEDIUM MATHEMATICAL SPACE */, 0x3000, 0x3000 /* IDEOGRAPHIC SPACE */];
// prettier-ignore-end

var isNonASCIISpaceCharacter = function isNonASCIISpaceCharacter(character) {
  return inRange(character, non_ASCII_space_characters);
};

// prettier-ignore-start
var non_ASCII_controls_characters = [
/**
 * C.2.2 Non-ASCII control characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.2.2
 */
0x0080, 0x009f /* [CONTROL CHARACTERS] */, 0x06dd, 0x06dd /* ARABIC END OF AYAH */, 0x070f, 0x070f /* SYRIAC ABBREVIATION MARK */, 0x180e, 0x180e /* MONGOLIAN VOWEL SEPARATOR */, 0x200c, 0x200c /* ZERO WIDTH NON-JOINER */, 0x200d, 0x200d /* ZERO WIDTH JOINER */, 0x2028, 0x2028 /* LINE SEPARATOR */, 0x2029, 0x2029 /* PARAGRAPH SEPARATOR */, 0x2060, 0x2060 /* WORD JOINER */, 0x2061, 0x2061 /* FUNCTION APPLICATION */, 0x2062, 0x2062 /* INVISIBLE TIMES */, 0x2063, 0x2063 /* INVISIBLE SEPARATOR */, 0x206a, 0x206f /* [CONTROL CHARACTERS] */, 0xfeff, 0xfeff /* ZERO WIDTH NO-BREAK SPACE */, 0xfff9, 0xfffc /* [CONTROL CHARACTERS] */, 0x1d173, 0x1d17a /* [MUSICAL CONTROL CHARACTERS] */];
var non_character_codepoints = [
/**
 * C.4 Non-character code points
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.4
 */
0xfdd0, 0xfdef /* [NONCHARACTER CODE POINTS] */, 0xfffe, 0xffff /* [NONCHARACTER CODE POINTS] */, 0x1fffe, 0x1ffff /* [NONCHARACTER CODE POINTS] */, 0x2fffe, 0x2ffff /* [NONCHARACTER CODE POINTS] */, 0x3fffe, 0x3ffff /* [NONCHARACTER CODE POINTS] */, 0x4fffe, 0x4ffff /* [NONCHARACTER CODE POINTS] */, 0x5fffe, 0x5ffff /* [NONCHARACTER CODE POINTS] */, 0x6fffe, 0x6ffff /* [NONCHARACTER CODE POINTS] */, 0x7fffe, 0x7ffff /* [NONCHARACTER CODE POINTS] */, 0x8fffe, 0x8ffff /* [NONCHARACTER CODE POINTS] */, 0x9fffe, 0x9ffff /* [NONCHARACTER CODE POINTS] */, 0xafffe, 0xaffff /* [NONCHARACTER CODE POINTS] */, 0xbfffe, 0xbffff /* [NONCHARACTER CODE POINTS] */, 0xcfffe, 0xcffff /* [NONCHARACTER CODE POINTS] */, 0xdfffe, 0xdffff /* [NONCHARACTER CODE POINTS] */, 0xefffe, 0xeffff /* [NONCHARACTER CODE POINTS] */, 0x10fffe, 0x10ffff /* [NONCHARACTER CODE POINTS] */];

/**
 * 2.3.  Prohibited Output
 */
var prohibited_characters = [
/**
 * C.2.1 ASCII control characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.2.1
 */
0, 0x001f /* [CONTROL CHARACTERS] */, 0x007f, 0x007f /* DELETE */,
/**
 * C.8 Change display properties or are deprecated
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.8
 */
0x0340, 0x0340 /* COMBINING GRAVE TONE MARK */, 0x0341, 0x0341 /* COMBINING ACUTE TONE MARK */, 0x200e, 0x200e /* LEFT-TO-RIGHT MARK */, 0x200f, 0x200f /* RIGHT-TO-LEFT MARK */, 0x202a, 0x202a /* LEFT-TO-RIGHT EMBEDDING */, 0x202b, 0x202b /* RIGHT-TO-LEFT EMBEDDING */, 0x202c, 0x202c /* POP DIRECTIONAL FORMATTING */, 0x202d, 0x202d /* LEFT-TO-RIGHT OVERRIDE */, 0x202e, 0x202e /* RIGHT-TO-LEFT OVERRIDE */, 0x206a, 0x206a /* INHIBIT SYMMETRIC SWAPPING */, 0x206b, 0x206b /* ACTIVATE SYMMETRIC SWAPPING */, 0x206c, 0x206c /* INHIBIT ARABIC FORM SHAPING */, 0x206d, 0x206d /* ACTIVATE ARABIC FORM SHAPING */, 0x206e, 0x206e /* NATIONAL DIGIT SHAPES */, 0x206f, 0x206f /* NOMINAL DIGIT SHAPES */,
/**
 * C.7 Inappropriate for canonical representation
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.7
 */
0x2ff0, 0x2ffb /* [IDEOGRAPHIC DESCRIPTION CHARACTERS] */,
/**
 * C.5 Surrogate codes
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.5
 */
0xd800, 0xdfff,
/**
 * C.3 Private use
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.3
 */
0xe000, 0xf8ff /* [PRIVATE USE, PLANE 0] */,
/**
 * C.6 Inappropriate for plain text
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.6
 */
0xfff9, 0xfff9 /* INTERLINEAR ANNOTATION ANCHOR */, 0xfffa, 0xfffa /* INTERLINEAR ANNOTATION SEPARATOR */, 0xfffb, 0xfffb /* INTERLINEAR ANNOTATION TERMINATOR */, 0xfffc, 0xfffc /* OBJECT REPLACEMENT CHARACTER */, 0xfffd, 0xfffd /* REPLACEMENT CHARACTER */,
/**
 * C.9 Tagging characters
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.9
 */
0xe0001, 0xe0001 /* LANGUAGE TAG */, 0xe0020, 0xe007f /* [TAGGING CHARACTERS] */,
/**
 * C.3 Private use
 * @link https://tools.ietf.org/html/rfc3454#appendix-C.3
 */

0xf0000, 0xffffd /* [PRIVATE USE, PLANE 15] */, 0x100000, 0x10fffd /* [PRIVATE USE, PLANE 16] */];
// prettier-ignore-end

var isProhibitedCharacter = function isProhibitedCharacter(character) {
  return inRange(character, non_ASCII_space_characters) || inRange(character, prohibited_characters) || inRange(character, non_ASCII_controls_characters) || inRange(character, non_character_codepoints);
};

// prettier-ignore-start
/**
 * D.1 Characters with bidirectional property "R" or "AL"
 * @link https://tools.ietf.org/html/rfc3454#appendix-D.1
 */
var bidirectional_r_al = [0x05be, 0x05be, 0x05c0, 0x05c0, 0x05c3, 0x05c3, 0x05d0, 0x05ea, 0x05f0, 0x05f4, 0x061b, 0x061b, 0x061f, 0x061f, 0x0621, 0x063a, 0x0640, 0x064a, 0x066d, 0x066f, 0x0671, 0x06d5, 0x06dd, 0x06dd, 0x06e5, 0x06e6, 0x06fa, 0x06fe, 0x0700, 0x070d, 0x0710, 0x0710, 0x0712, 0x072c, 0x0780, 0x07a5, 0x07b1, 0x07b1, 0x200f, 0x200f, 0xfb1d, 0xfb1d, 0xfb1f, 0xfb28, 0xfb2a, 0xfb36, 0xfb38, 0xfb3c, 0xfb3e, 0xfb3e, 0xfb40, 0xfb41, 0xfb43, 0xfb44, 0xfb46, 0xfbb1, 0xfbd3, 0xfd3d, 0xfd50, 0xfd8f, 0xfd92, 0xfdc7, 0xfdf0, 0xfdfc, 0xfe70, 0xfe74, 0xfe76, 0xfefc];
// prettier-ignore-end

var isBidirectionalRAL = function isBidirectionalRAL(character) {
  return inRange(character, bidirectional_r_al);
};

// prettier-ignore-start
/**
 * D.2 Characters with bidirectional property "L"
 * @link https://tools.ietf.org/html/rfc3454#appendix-D.2
 */
var bidirectional_l = [0x0041, 0x005a, 0x0061, 0x007a, 0x00aa, 0x00aa, 0x00b5, 0x00b5, 0x00ba, 0x00ba, 0x00c0, 0x00d6, 0x00d8, 0x00f6, 0x00f8, 0x0220, 0x0222, 0x0233, 0x0250, 0x02ad, 0x02b0, 0x02b8, 0x02bb, 0x02c1, 0x02d0, 0x02d1, 0x02e0, 0x02e4, 0x02ee, 0x02ee, 0x037a, 0x037a, 0x0386, 0x0386, 0x0388, 0x038a, 0x038c, 0x038c, 0x038e, 0x03a1, 0x03a3, 0x03ce, 0x03d0, 0x03f5, 0x0400, 0x0482, 0x048a, 0x04ce, 0x04d0, 0x04f5, 0x04f8, 0x04f9, 0x0500, 0x050f, 0x0531, 0x0556, 0x0559, 0x055f, 0x0561, 0x0587, 0x0589, 0x0589, 0x0903, 0x0903, 0x0905, 0x0939, 0x093d, 0x0940, 0x0949, 0x094c, 0x0950, 0x0950, 0x0958, 0x0961, 0x0964, 0x0970, 0x0982, 0x0983, 0x0985, 0x098c, 0x098f, 0x0990, 0x0993, 0x09a8, 0x09aa, 0x09b0, 0x09b2, 0x09b2, 0x09b6, 0x09b9, 0x09be, 0x09c0, 0x09c7, 0x09c8, 0x09cb, 0x09cc, 0x09d7, 0x09d7, 0x09dc, 0x09dd, 0x09df, 0x09e1, 0x09e6, 0x09f1, 0x09f4, 0x09fa, 0x0a05, 0x0a0a, 0x0a0f, 0x0a10, 0x0a13, 0x0a28, 0x0a2a, 0x0a30, 0x0a32, 0x0a33, 0x0a35, 0x0a36, 0x0a38, 0x0a39, 0x0a3e, 0x0a40, 0x0a59, 0x0a5c, 0x0a5e, 0x0a5e, 0x0a66, 0x0a6f, 0x0a72, 0x0a74, 0x0a83, 0x0a83, 0x0a85, 0x0a8b, 0x0a8d, 0x0a8d, 0x0a8f, 0x0a91, 0x0a93, 0x0aa8, 0x0aaa, 0x0ab0, 0x0ab2, 0x0ab3, 0x0ab5, 0x0ab9, 0x0abd, 0x0ac0, 0x0ac9, 0x0ac9, 0x0acb, 0x0acc, 0x0ad0, 0x0ad0, 0x0ae0, 0x0ae0, 0x0ae6, 0x0aef, 0x0b02, 0x0b03, 0x0b05, 0x0b0c, 0x0b0f, 0x0b10, 0x0b13, 0x0b28, 0x0b2a, 0x0b30, 0x0b32, 0x0b33, 0x0b36, 0x0b39, 0x0b3d, 0x0b3e, 0x0b40, 0x0b40, 0x0b47, 0x0b48, 0x0b4b, 0x0b4c, 0x0b57, 0x0b57, 0x0b5c, 0x0b5d, 0x0b5f, 0x0b61, 0x0b66, 0x0b70, 0x0b83, 0x0b83, 0x0b85, 0x0b8a, 0x0b8e, 0x0b90, 0x0b92, 0x0b95, 0x0b99, 0x0b9a, 0x0b9c, 0x0b9c, 0x0b9e, 0x0b9f, 0x0ba3, 0x0ba4, 0x0ba8, 0x0baa, 0x0bae, 0x0bb5, 0x0bb7, 0x0bb9, 0x0bbe, 0x0bbf, 0x0bc1, 0x0bc2, 0x0bc6, 0x0bc8, 0x0bca, 0x0bcc, 0x0bd7, 0x0bd7, 0x0be7, 0x0bf2, 0x0c01, 0x0c03, 0x0c05, 0x0c0c, 0x0c0e, 0x0c10, 0x0c12, 0x0c28, 0x0c2a, 0x0c33, 0x0c35, 0x0c39, 0x0c41, 0x0c44, 0x0c60, 0x0c61, 0x0c66, 0x0c6f, 0x0c82, 0x0c83, 0x0c85, 0x0c8c, 0x0c8e, 0x0c90, 0x0c92, 0x0ca8, 0x0caa, 0x0cb3, 0x0cb5, 0x0cb9, 0x0cbe, 0x0cbe, 0x0cc0, 0x0cc4, 0x0cc7, 0x0cc8, 0x0cca, 0x0ccb, 0x0cd5, 0x0cd6, 0x0cde, 0x0cde, 0x0ce0, 0x0ce1, 0x0ce6, 0x0cef, 0x0d02, 0x0d03, 0x0d05, 0x0d0c, 0x0d0e, 0x0d10, 0x0d12, 0x0d28, 0x0d2a, 0x0d39, 0x0d3e, 0x0d40, 0x0d46, 0x0d48, 0x0d4a, 0x0d4c, 0x0d57, 0x0d57, 0x0d60, 0x0d61, 0x0d66, 0x0d6f, 0x0d82, 0x0d83, 0x0d85, 0x0d96, 0x0d9a, 0x0db1, 0x0db3, 0x0dbb, 0x0dbd, 0x0dbd, 0x0dc0, 0x0dc6, 0x0dcf, 0x0dd1, 0x0dd8, 0x0ddf, 0x0df2, 0x0df4, 0x0e01, 0x0e30, 0x0e32, 0x0e33, 0x0e40, 0x0e46, 0x0e4f, 0x0e5b, 0x0e81, 0x0e82, 0x0e84, 0x0e84, 0x0e87, 0x0e88, 0x0e8a, 0x0e8a, 0x0e8d, 0x0e8d, 0x0e94, 0x0e97, 0x0e99, 0x0e9f, 0x0ea1, 0x0ea3, 0x0ea5, 0x0ea5, 0x0ea7, 0x0ea7, 0x0eaa, 0x0eab, 0x0ead, 0x0eb0, 0x0eb2, 0x0eb3, 0x0ebd, 0x0ebd, 0x0ec0, 0x0ec4, 0x0ec6, 0x0ec6, 0x0ed0, 0x0ed9, 0x0edc, 0x0edd, 0x0f00, 0x0f17, 0x0f1a, 0x0f34, 0x0f36, 0x0f36, 0x0f38, 0x0f38, 0x0f3e, 0x0f47, 0x0f49, 0x0f6a, 0x0f7f, 0x0f7f, 0x0f85, 0x0f85, 0x0f88, 0x0f8b, 0x0fbe, 0x0fc5, 0x0fc7, 0x0fcc, 0x0fcf, 0x0fcf, 0x1000, 0x1021, 0x1023, 0x1027, 0x1029, 0x102a, 0x102c, 0x102c, 0x1031, 0x1031, 0x1038, 0x1038, 0x1040, 0x1057, 0x10a0, 0x10c5, 0x10d0, 0x10f8, 0x10fb, 0x10fb, 0x1100, 0x1159, 0x115f, 0x11a2, 0x11a8, 0x11f9, 0x1200, 0x1206, 0x1208, 0x1246, 0x1248, 0x1248, 0x124a, 0x124d, 0x1250, 0x1256, 0x1258, 0x1258, 0x125a, 0x125d, 0x1260, 0x1286, 0x1288, 0x1288, 0x128a, 0x128d, 0x1290, 0x12ae, 0x12b0, 0x12b0, 0x12b2, 0x12b5, 0x12b8, 0x12be, 0x12c0, 0x12c0, 0x12c2, 0x12c5, 0x12c8, 0x12ce, 0x12d0, 0x12d6, 0x12d8, 0x12ee, 0x12f0, 0x130e, 0x1310, 0x1310, 0x1312, 0x1315, 0x1318, 0x131e, 0x1320, 0x1346, 0x1348, 0x135a, 0x1361, 0x137c, 0x13a0, 0x13f4, 0x1401, 0x1676, 0x1681, 0x169a, 0x16a0, 0x16f0, 0x1700, 0x170c, 0x170e, 0x1711, 0x1720, 0x1731, 0x1735, 0x1736, 0x1740, 0x1751, 0x1760, 0x176c, 0x176e, 0x1770, 0x1780, 0x17b6, 0x17be, 0x17c5, 0x17c7, 0x17c8, 0x17d4, 0x17da, 0x17dc, 0x17dc, 0x17e0, 0x17e9, 0x1810, 0x1819, 0x1820, 0x1877, 0x1880, 0x18a8, 0x1e00, 0x1e9b, 0x1ea0, 0x1ef9, 0x1f00, 0x1f15, 0x1f18, 0x1f1d, 0x1f20, 0x1f45, 0x1f48, 0x1f4d, 0x1f50, 0x1f57, 0x1f59, 0x1f59, 0x1f5b, 0x1f5b, 0x1f5d, 0x1f5d, 0x1f5f, 0x1f7d, 0x1f80, 0x1fb4, 0x1fb6, 0x1fbc, 0x1fbe, 0x1fbe, 0x1fc2, 0x1fc4, 0x1fc6, 0x1fcc, 0x1fd0, 0x1fd3, 0x1fd6, 0x1fdb, 0x1fe0, 0x1fec, 0x1ff2, 0x1ff4, 0x1ff6, 0x1ffc, 0x200e, 0x200e, 0x2071, 0x2071, 0x207f, 0x207f, 0x2102, 0x2102, 0x2107, 0x2107, 0x210a, 0x2113, 0x2115, 0x2115, 0x2119, 0x211d, 0x2124, 0x2124, 0x2126, 0x2126, 0x2128, 0x2128, 0x212a, 0x212d, 0x212f, 0x2131, 0x2133, 0x2139, 0x213d, 0x213f, 0x2145, 0x2149, 0x2160, 0x2183, 0x2336, 0x237a, 0x2395, 0x2395, 0x249c, 0x24e9, 0x3005, 0x3007, 0x3021, 0x3029, 0x3031, 0x3035, 0x3038, 0x303c, 0x3041, 0x3096, 0x309d, 0x309f, 0x30a1, 0x30fa, 0x30fc, 0x30ff, 0x3105, 0x312c, 0x3131, 0x318e, 0x3190, 0x31b7, 0x31f0, 0x321c, 0x3220, 0x3243, 0x3260, 0x327b, 0x327f, 0x32b0, 0x32c0, 0x32cb, 0x32d0, 0x32fe, 0x3300, 0x3376, 0x337b, 0x33dd, 0x33e0, 0x33fe, 0x3400, 0x4db5, 0x4e00, 0x9fa5, 0xa000, 0xa48c, 0xac00, 0xd7a3, 0xd800, 0xfa2d, 0xfa30, 0xfa6a, 0xfb00, 0xfb06, 0xfb13, 0xfb17, 0xff21, 0xff3a, 0xff41, 0xff5a, 0xff66, 0xffbe, 0xffc2, 0xffc7, 0xffca, 0xffcf, 0xffd2, 0xffd7, 0xffda, 0xffdc, 0x10300, 0x1031e, 0x10320, 0x10323, 0x10330, 0x1034a, 0x10400, 0x10425, 0x10428, 0x1044d, 0x1d000, 0x1d0f5, 0x1d100, 0x1d126, 0x1d12a, 0x1d166, 0x1d16a, 0x1d172, 0x1d183, 0x1d184, 0x1d18c, 0x1d1a9, 0x1d1ae, 0x1d1dd, 0x1d400, 0x1d454, 0x1d456, 0x1d49c, 0x1d49e, 0x1d49f, 0x1d4a2, 0x1d4a2, 0x1d4a5, 0x1d4a6, 0x1d4a9, 0x1d4ac, 0x1d4ae, 0x1d4b9, 0x1d4bb, 0x1d4bb, 0x1d4bd, 0x1d4c0, 0x1d4c2, 0x1d4c3, 0x1d4c5, 0x1d505, 0x1d507, 0x1d50a, 0x1d50d, 0x1d514, 0x1d516, 0x1d51c, 0x1d51e, 0x1d539, 0x1d53b, 0x1d53e, 0x1d540, 0x1d544, 0x1d546, 0x1d546, 0x1d54a, 0x1d550, 0x1d552, 0x1d6a3, 0x1d6a8, 0x1d7c9, 0x20000, 0x2a6d6, 0x2f800, 0x2fa1d, 0xf0000, 0xffffd, 0x100000, 0x10fffd];
// prettier-ignore-end

var isBidirectionalL = function isBidirectionalL(character) {
  return inRange(character, bidirectional_l);
};

// 2.1.  Mapping

/**
 * non-ASCII space characters [StringPrep, C.1.2] that can be
 * mapped to SPACE (U+0020)
 */
var mapping2space = isNonASCIISpaceCharacter;

/**
 * the "commonly mapped to nothing" characters [StringPrep, B.1]
 * that can be mapped to nothing.
 */
var mapping2nothing = isCommonlyMappedToNothing;

// utils
var getCodePoint = function getCodePoint(character) {
  return character.codePointAt(0);
};
var first = function first(x) {
  return x[0];
};
var last = function last(x) {
  return x[x.length - 1];
};

/**
 * Convert provided string into an array of Unicode Code Points.
 * Based on https://stackoverflow.com/a/21409165/1556249
 * and https://www.npmjs.com/package/code-point-at.
 * @param {string} input
 * @returns {number[]}
 */
function toCodePoints(input) {
  var codepoints = [];
  var size = input.length;
  for (var i = 0; i < size; i += 1) {
    var before = input.charCodeAt(i);
    if (before >= 0xd800 && before <= 0xdbff && size > i + 1) {
      var next = input.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        codepoints.push((before - 0xd800) * 0x400 + next - 0xdc00 + 0x10000);
        i += 1;
        continue;
      }
    }
    codepoints.push(before);
  }
  return codepoints;
}

/**
 * SASLprep.
 * @param {string} input
 * @param {Object} opts
 * @param {boolean} opts.allowUnassigned
 * @returns {string}
 */
function saslprep(input) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (typeof input !== 'string') {
    throw new TypeError('Expected string.');
  }
  if (input.length === 0) {
    return '';
  }

  // 1. Map
  var mapped_input = toCodePoints(input)
  // 1.1 mapping to space
  .map(function (character) {
    return mapping2space(character) ? 0x20 : character;
  })
  // 1.2 mapping to nothing
  .filter(function (character) {
    return !mapping2nothing(character);
  });

  // 2. Normalize
  var normalized_input = String.fromCodePoint.apply(null, mapped_input).normalize('NFKC');
  var normalized_map = toCodePoints(normalized_input);

  // 3. Prohibit
  var hasProhibited = normalized_map.some(isProhibitedCharacter);
  if (hasProhibited) {
    throw new Error('Prohibited character, see https://tools.ietf.org/html/rfc4013#section-2.3');
  }

  // Unassigned Code Points
  if (opts.allowUnassigned !== true) {
    var hasUnassigned = normalized_map.some(isUnassignedCodePoint);
    if (hasUnassigned) {
      throw new Error('Unassigned code point, see https://tools.ietf.org/html/rfc4013#section-2.5');
    }
  }

  // 4. check bidi

  var hasBidiRAL = normalized_map.some(isBidirectionalRAL);
  var hasBidiL = normalized_map.some(isBidirectionalL);

  // 4.1 If a string contains any RandALCat character, the string MUST NOT
  // contain any LCat character.
  if (hasBidiRAL && hasBidiL) {
    throw new Error('String must not contain RandALCat and LCat at the same time,' + ' see https://tools.ietf.org/html/rfc3454#section-6');
  }

  /**
   * 4.2 If a string contains any RandALCat character, a RandALCat
   * character MUST be the first character of the string, and a
   * RandALCat character MUST be the last character of the string.
   */

  var isFirstBidiRAL = isBidirectionalRAL(getCodePoint(first(normalized_input)));
  var isLastBidiRAL = isBidirectionalRAL(getCodePoint(last(normalized_input)));
  if (hasBidiRAL && !(isFirstBidiRAL && isLastBidiRAL)) {
    throw new Error('Bidirectional RandALCat character must be the first and the last' + ' character of the string, see https://tools.ietf.org/html/rfc3454#section-6');
  }
  return normalized_input;
}

/*
   PDFSecurity - represents PDF security settings
   By Yang Liu <hi@zesik.com>
 */
var PDFSecurity = /*#__PURE__*/function () {
  function PDFSecurity(document) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!options.ownerPassword && !options.userPassword) {
      throw new Error('None of owner password and user password is defined.');
    }
    this.document = document;
    this._setupEncryption(options);
  }
  PDFSecurity.generateFileID = function generateFileID() {
    var info = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var infoStr = info.CreationDate.getTime() + "\n";
    for (var key in info) {
      // eslint-disable-next-line no-prototype-builtins
      if (!info.hasOwnProperty(key)) {
        continue;
      }
      infoStr += key + ": " + info[key].valueOf() + "\n";
    }
    return wordArrayToBuffer(_cryptoJs.default.MD5(infoStr));
  };
  PDFSecurity.generateRandomWordArray = function generateRandomWordArray(bytes) {
    return _cryptoJs.default.lib.WordArray.random(bytes);
  };
  PDFSecurity.create = function create(document) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if (!options.ownerPassword && !options.userPassword) {
      return null;
    }
    return new PDFSecurity(document, options);
  };
  var _proto7 = PDFSecurity.prototype;
  _proto7._setupEncryption = function _setupEncryption(options) {
    switch (options.pdfVersion) {
      case '1.4':
      case '1.5':
        this.version = 2;
        break;
      case '1.6':
      case '1.7':
        this.version = 4;
        break;
      case '1.7ext3':
        this.version = 5;
        break;
      default:
        this.version = 1;
        break;
    }
    var encDict = {
      Filter: 'Standard'
    };
    switch (this.version) {
      case 1:
      case 2:
      case 4:
        this._setupEncryptionV1V2V4(this.version, encDict, options);
        break;
      case 5:
        this._setupEncryptionV5(encDict, options);
        break;
    }
    this.dictionary = this.document.ref(encDict);
  };
  _proto7._setupEncryptionV1V2V4 = function _setupEncryptionV1V2V4(v, encDict, options) {
    var r, permissions;
    switch (v) {
      case 1:
        r = 2;
        this.keyBits = 40;
        permissions = getPermissionsR2(options.permissions);
        break;
      case 2:
        r = 3;
        this.keyBits = 128;
        permissions = getPermissionsR3(options.permissions);
        break;
      case 4:
        r = 4;
        this.keyBits = 128;
        permissions = getPermissionsR3(options.permissions);
        break;
    }
    var paddedUserPassword = processPasswordR2R3R4(options.userPassword);
    var paddedOwnerPassword = options.ownerPassword ? processPasswordR2R3R4(options.ownerPassword) : paddedUserPassword;
    var ownerPasswordEntry = getOwnerPasswordR2R3R4(r, this.keyBits, paddedUserPassword, paddedOwnerPassword);
    this.encryptionKey = getEncryptionKeyR2R3R4(r, this.keyBits, this.document._id, paddedUserPassword, ownerPasswordEntry, permissions);
    var userPasswordEntry;
    if (r === 2) {
      userPasswordEntry = getUserPasswordR2(this.encryptionKey);
    } else {
      userPasswordEntry = getUserPasswordR3R4(this.document._id, this.encryptionKey);
    }
    encDict.V = v;
    if (v >= 2) {
      encDict.Length = this.keyBits;
    }
    if (v === 4) {
      encDict.CF = {
        StdCF: {
          AuthEvent: 'DocOpen',
          CFM: 'AESV2',
          Length: this.keyBits / 8
        }
      };
      encDict.StmF = 'StdCF';
      encDict.StrF = 'StdCF';
    }
    encDict.R = r;
    encDict.O = wordArrayToBuffer(ownerPasswordEntry);
    encDict.U = wordArrayToBuffer(userPasswordEntry);
    encDict.P = permissions;
  };
  _proto7._setupEncryptionV5 = function _setupEncryptionV5(encDict, options) {
    this.keyBits = 256;
    var permissions = getPermissionsR3(options.permissions);
    var processedUserPassword = processPasswordR5(options.userPassword);
    var processedOwnerPassword = options.ownerPassword ? processPasswordR5(options.ownerPassword) : processedUserPassword;
    this.encryptionKey = getEncryptionKeyR5(PDFSecurity.generateRandomWordArray);
    var userPasswordEntry = getUserPasswordR5(processedUserPassword, PDFSecurity.generateRandomWordArray);
    var userKeySalt = _cryptoJs.default.lib.WordArray.create(userPasswordEntry.words.slice(10, 12), 8);
    var userEncryptionKeyEntry = getUserEncryptionKeyR5(processedUserPassword, userKeySalt, this.encryptionKey);
    var ownerPasswordEntry = getOwnerPasswordR5(processedOwnerPassword, userPasswordEntry, PDFSecurity.generateRandomWordArray);
    var ownerKeySalt = _cryptoJs.default.lib.WordArray.create(ownerPasswordEntry.words.slice(10, 12), 8);
    var ownerEncryptionKeyEntry = getOwnerEncryptionKeyR5(processedOwnerPassword, ownerKeySalt, userPasswordEntry, this.encryptionKey);
    var permsEntry = getEncryptedPermissionsR5(permissions, this.encryptionKey, PDFSecurity.generateRandomWordArray);
    encDict.V = 5;
    encDict.Length = this.keyBits;
    encDict.CF = {
      StdCF: {
        AuthEvent: 'DocOpen',
        CFM: 'AESV3',
        Length: this.keyBits / 8
      }
    };
    encDict.StmF = 'StdCF';
    encDict.StrF = 'StdCF';
    encDict.R = 5;
    encDict.O = wordArrayToBuffer(ownerPasswordEntry);
    encDict.OE = wordArrayToBuffer(ownerEncryptionKeyEntry);
    encDict.U = wordArrayToBuffer(userPasswordEntry);
    encDict.UE = wordArrayToBuffer(userEncryptionKeyEntry);
    encDict.P = permissions;
    encDict.Perms = wordArrayToBuffer(permsEntry);
  };
  _proto7.getEncryptFn = function getEncryptFn(obj, gen) {
    var digest;
    if (this.version < 5) {
      digest = this.encryptionKey.clone().concat(_cryptoJs.default.lib.WordArray.create([(obj & 0xff) << 24 | (obj & 0xff00) << 8 | obj >> 8 & 0xff00 | gen & 0xff, (gen & 0xff00) << 16], 5));
    }
    if (this.version === 1 || this.version === 2) {
      var _key2 = _cryptoJs.default.MD5(digest);
      _key2.sigBytes = Math.min(16, this.keyBits / 8 + 5);
      return function (buffer) {
        return wordArrayToBuffer(_cryptoJs.default.RC4.encrypt(_cryptoJs.default.lib.WordArray.create(buffer), _key2).ciphertext);
      };
    }
    var key;
    if (this.version === 4) {
      key = _cryptoJs.default.MD5(digest.concat(_cryptoJs.default.lib.WordArray.create([0x73416c54], 4)));
    } else {
      key = this.encryptionKey;
    }
    var iv = PDFSecurity.generateRandomWordArray(16);
    var options = {
      mode: _cryptoJs.default.mode.CBC,
      padding: _cryptoJs.default.pad.Pkcs7,
      iv: iv
    };
    return function (buffer) {
      return wordArrayToBuffer(iv.clone().concat(_cryptoJs.default.AES.encrypt(_cryptoJs.default.lib.WordArray.create(buffer), key, options).ciphertext));
    };
  };
  _proto7.end = function end() {
    this.dictionary.end();
  };
  return PDFSecurity;
}();
function getPermissionsR2() {
  var permissionObject = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var permissions = 0xffffffc0 >> 0;
  if (permissionObject.printing) {
    permissions |= 4;
  }
  if (permissionObject.modifying) {
    permissions |= 8;
  }
  if (permissionObject.copying) {
    permissions |= 16;
  }
  if (permissionObject.annotating) {
    permissions |= 32;
  }
  return permissions;
}
function getPermissionsR3() {
  var permissionObject = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var permissions = 0xfffff0c0 >> 0;
  if (permissionObject.printing === 'lowResolution') {
    permissions |= 4;
  }
  if (permissionObject.printing === 'highResolution') {
    permissions |= 2052;
  }
  if (permissionObject.modifying) {
    permissions |= 8;
  }
  if (permissionObject.copying) {
    permissions |= 16;
  }
  if (permissionObject.annotating) {
    permissions |= 32;
  }
  if (permissionObject.fillingForms) {
    permissions |= 256;
  }
  if (permissionObject.contentAccessibility) {
    permissions |= 512;
  }
  if (permissionObject.documentAssembly) {
    permissions |= 1024;
  }
  return permissions;
}
function getUserPasswordR2(encryptionKey) {
  return _cryptoJs.default.RC4.encrypt(processPasswordR2R3R4(), encryptionKey).ciphertext;
}
function getUserPasswordR3R4(documentId, encryptionKey) {
  var key = encryptionKey.clone();
  var cipher = _cryptoJs.default.MD5(processPasswordR2R3R4().concat(_cryptoJs.default.lib.WordArray.create(documentId)));
  for (var i = 0; i < 20; i++) {
    var xorRound = Math.ceil(key.sigBytes / 4);
    for (var j = 0; j < xorRound; j++) {
      key.words[j] = encryptionKey.words[j] ^ (i | i << 8 | i << 16 | i << 24);
    }
    cipher = _cryptoJs.default.RC4.encrypt(cipher, key).ciphertext;
  }
  return cipher.concat(_cryptoJs.default.lib.WordArray.create(null, 16));
}
function getOwnerPasswordR2R3R4(r, keyBits, paddedUserPassword, paddedOwnerPassword) {
  var digest = paddedOwnerPassword;
  var round = r >= 3 ? 51 : 1;
  for (var i = 0; i < round; i++) {
    digest = _cryptoJs.default.MD5(digest);
  }
  var key = digest.clone();
  key.sigBytes = keyBits / 8;
  var cipher = paddedUserPassword;
  round = r >= 3 ? 20 : 1;
  for (var _i2 = 0; _i2 < round; _i2++) {
    var xorRound = Math.ceil(key.sigBytes / 4);
    for (var j = 0; j < xorRound; j++) {
      key.words[j] = digest.words[j] ^ (_i2 | _i2 << 8 | _i2 << 16 | _i2 << 24);
    }
    cipher = _cryptoJs.default.RC4.encrypt(cipher, key).ciphertext;
  }
  return cipher;
}
function getEncryptionKeyR2R3R4(r, keyBits, documentId, paddedUserPassword, ownerPasswordEntry, permissions) {
  var key = paddedUserPassword.clone().concat(ownerPasswordEntry).concat(_cryptoJs.default.lib.WordArray.create([lsbFirstWord(permissions)], 4)).concat(_cryptoJs.default.lib.WordArray.create(documentId));
  var round = r >= 3 ? 51 : 1;
  for (var i = 0; i < round; i++) {
    key = _cryptoJs.default.MD5(key);
    key.sigBytes = keyBits / 8;
  }
  return key;
}
function getUserPasswordR5(processedUserPassword, generateRandomWordArray) {
  var validationSalt = generateRandomWordArray(8);
  var keySalt = generateRandomWordArray(8);
  return _cryptoJs.default.SHA256(processedUserPassword.clone().concat(validationSalt)).concat(validationSalt).concat(keySalt);
}
function getUserEncryptionKeyR5(processedUserPassword, userKeySalt, encryptionKey) {
  var key = _cryptoJs.default.SHA256(processedUserPassword.clone().concat(userKeySalt));
  var options = {
    mode: _cryptoJs.default.mode.CBC,
    padding: _cryptoJs.default.pad.NoPadding,
    iv: _cryptoJs.default.lib.WordArray.create(null, 16)
  };
  return _cryptoJs.default.AES.encrypt(encryptionKey, key, options).ciphertext;
}
function getOwnerPasswordR5(processedOwnerPassword, userPasswordEntry, generateRandomWordArray) {
  var validationSalt = generateRandomWordArray(8);
  var keySalt = generateRandomWordArray(8);
  return _cryptoJs.default.SHA256(processedOwnerPassword.clone().concat(validationSalt).concat(userPasswordEntry)).concat(validationSalt).concat(keySalt);
}
function getOwnerEncryptionKeyR5(processedOwnerPassword, ownerKeySalt, userPasswordEntry, encryptionKey) {
  var key = _cryptoJs.default.SHA256(processedOwnerPassword.clone().concat(ownerKeySalt).concat(userPasswordEntry));
  var options = {
    mode: _cryptoJs.default.mode.CBC,
    padding: _cryptoJs.default.pad.NoPadding,
    iv: _cryptoJs.default.lib.WordArray.create(null, 16)
  };
  return _cryptoJs.default.AES.encrypt(encryptionKey, key, options).ciphertext;
}
function getEncryptionKeyR5(generateRandomWordArray) {
  return generateRandomWordArray(32);
}
function getEncryptedPermissionsR5(permissions, encryptionKey, generateRandomWordArray) {
  var cipher = _cryptoJs.default.lib.WordArray.create([lsbFirstWord(permissions), 0xffffffff, 0x54616462], 12).concat(generateRandomWordArray(4));
  var options = {
    mode: _cryptoJs.default.mode.ECB,
    padding: _cryptoJs.default.pad.NoPadding
  };
  return _cryptoJs.default.AES.encrypt(cipher, encryptionKey, options).ciphertext;
}
function processPasswordR2R3R4() {
  var password = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  var out = Buffer.alloc(32);
  var length = password.length;
  var index = 0;
  while (index < length && index < 32) {
    var code = password.charCodeAt(index);
    if (code > 0xff) {
      throw new Error('Password contains one or more invalid characters.');
    }
    out[index] = code;
    index++;
  }
  while (index < 32) {
    out[index] = PASSWORD_PADDING[index - length];
    index++;
  }
  return _cryptoJs.default.lib.WordArray.create(out);
}
function processPasswordR5() {
  var password = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  password = unescape(encodeURIComponent(saslprep(password)));
  var length = Math.min(127, password.length);
  var out = Buffer.alloc(length);
  for (var i = 0; i < length; i++) {
    out[i] = password.charCodeAt(i);
  }
  return _cryptoJs.default.lib.WordArray.create(out);
}
function lsbFirstWord(data) {
  return (data & 0xff) << 24 | (data & 0xff00) << 8 | data >> 8 & 0xff00 | data >> 24 & 0xff;
}
function wordArrayToBuffer(wordArray) {
  var byteArray = [];
  for (var i = 0; i < wordArray.sigBytes; i++) {
    byteArray.push(wordArray.words[Math.floor(i / 4)] >> 8 * (3 - i % 4) & 0xff);
  }
  return Buffer.from(byteArray);
}
var PASSWORD_PADDING = [0x28, 0xbf, 0x4e, 0x5e, 0x4e, 0x75, 0x8a, 0x41, 0x64, 0x00, 0x4e, 0x56, 0xff, 0xfa, 0x01, 0x08, 0x2e, 0x2e, 0x00, 0xb6, 0xd0, 0x68, 0x3e, 0x80, 0x2f, 0x0c, 0xa9, 0xfe, 0x64, 0x53, 0x69, 0x7a];
var number = PDFObject.number;
var PDFGradient = /*#__PURE__*/function () {
  function PDFGradient(doc) {
    this.doc = doc;
    this.stops = [];
    this.embedded = false;
    this.transform = [1, 0, 0, 1, 0, 0];
  }
  var _proto8 = PDFGradient.prototype;
  _proto8.stop = function stop(pos, color, opacity) {
    if (opacity == null) {
      opacity = 1;
    }
    color = this.doc._normalizeColor(color);
    if (this.stops.length === 0) {
      if (color.length === 3) {
        this._colorSpace = 'DeviceRGB';
      } else if (color.length === 4) {
        this._colorSpace = 'DeviceCMYK';
      } else if (color.length === 1) {
        this._colorSpace = 'DeviceGray';
      } else {
        throw new Error('Unknown color space');
      }
    } else if (this._colorSpace === 'DeviceRGB' && color.length !== 3 || this._colorSpace === 'DeviceCMYK' && color.length !== 4 || this._colorSpace === 'DeviceGray' && color.length !== 1) {
      throw new Error('All gradient stops must use the same color space');
    }
    opacity = Math.max(0, Math.min(1, opacity));
    this.stops.push([pos, color, opacity]);
    return this;
  };
  _proto8.setTransform = function setTransform(m11, m12, m21, m22, dx, dy) {
    this.transform = [m11, m12, m21, m22, dx, dy];
    return this;
  };
  _proto8.embed = function embed(m) {
    var fn;
    var stopsLength = this.stops.length;
    if (stopsLength === 0) {
      return;
    }
    this.embedded = true;
    this.matrix = m;

    // if the last stop comes before 100%, add a copy at 100%
    var last = this.stops[stopsLength - 1];
    if (last[0] < 1) {
      this.stops.push([1, last[1], last[2]]);
    }
    var bounds = [];
    var encode = [];
    var stops = [];
    for (var i = 0; i < stopsLength - 1; i++) {
      encode.push(0, 1);
      if (i + 2 !== stopsLength) {
        bounds.push(this.stops[i + 1][0]);
      }
      fn = this.doc.ref({
        FunctionType: 2,
        Domain: [0, 1],
        C0: this.stops[i + 0][1],
        C1: this.stops[i + 1][1],
        N: 1
      });
      stops.push(fn);
      fn.end();
    }

    // if there are only two stops, we don't need a stitching function
    if (stopsLength === 1) {
      fn = stops[0];
    } else {
      fn = this.doc.ref({
        FunctionType: 3,
        // stitching function
        Domain: [0, 1],
        Functions: stops,
        Bounds: bounds,
        Encode: encode
      });
      fn.end();
    }
    this.id = "Sh" + ++this.doc._gradCount;
    var shader = this.shader(fn);
    shader.end();
    var pattern = this.doc.ref({
      Type: 'Pattern',
      PatternType: 2,
      Shading: shader,
      Matrix: this.matrix.map(number)
    });
    pattern.end();
    if (this.stops.some(function (stop) {
      return stop[2] < 1;
    })) {
      var grad = this.opacityGradient();
      grad._colorSpace = 'DeviceGray';
      for (var _iterator2 = _createForOfIteratorHelperLoose(this.stops), _step2; !(_step2 = _iterator2()).done;) {
        var stop = _step2.value;
        grad.stop(stop[0], [stop[2]]);
      }
      grad = grad.embed(this.matrix);
      var pageBBox = [0, 0, this.doc.page.width, this.doc.page.height];
      var form = this.doc.ref({
        Type: 'XObject',
        Subtype: 'Form',
        FormType: 1,
        BBox: pageBBox,
        Group: {
          Type: 'Group',
          S: 'Transparency',
          CS: 'DeviceGray'
        },
        Resources: {
          ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
          Pattern: {
            Sh1: grad
          }
        }
      });
      form.write('/Pattern cs /Sh1 scn');
      form.end(pageBBox.join(' ') + " re f");
      var gstate = this.doc.ref({
        Type: 'ExtGState',
        SMask: {
          Type: 'Mask',
          S: 'Luminosity',
          G: form
        }
      });
      gstate.end();
      var opacityPattern = this.doc.ref({
        Type: 'Pattern',
        PatternType: 1,
        PaintType: 1,
        TilingType: 2,
        BBox: pageBBox,
        XStep: pageBBox[2],
        YStep: pageBBox[3],
        Resources: {
          ProcSet: ['PDF', 'Text', 'ImageB', 'ImageC', 'ImageI'],
          Pattern: {
            Sh1: pattern
          },
          ExtGState: {
            Gs1: gstate
          }
        }
      });
      opacityPattern.write('/Gs1 gs /Pattern cs /Sh1 scn');
      opacityPattern.end(pageBBox.join(' ') + " re f");
      this.doc.page.patterns[this.id] = opacityPattern;
    } else {
      this.doc.page.patterns[this.id] = pattern;
    }
    return pattern;
  };
  _proto8.apply = function apply(stroke) {
    // apply gradient transform to existing document ctm
    var _this$doc$_ctm = this.doc._ctm,
      m0 = _this$doc$_ctm[0],
      m1 = _this$doc$_ctm[1],
      m2 = _this$doc$_ctm[2],
      m3 = _this$doc$_ctm[3],
      m4 = _this$doc$_ctm[4],
      m5 = _this$doc$_ctm[5];
    var _this$transform = this.transform,
      m11 = _this$transform[0],
      m12 = _this$transform[1],
      m21 = _this$transform[2],
      m22 = _this$transform[3],
      dx = _this$transform[4],
      dy = _this$transform[5];
    var m = [m0 * m11 + m2 * m12, m1 * m11 + m3 * m12, m0 * m21 + m2 * m22, m1 * m21 + m3 * m22, m0 * dx + m2 * dy + m4, m1 * dx + m3 * dy + m5];
    if (!this.embedded || m.join(' ') !== this.matrix.join(' ')) {
      this.embed(m);
    }
    this.doc._setColorSpace('Pattern', stroke);
    var op = stroke ? 'SCN' : 'scn';
    return this.doc.addContent("/" + this.id + " " + op);
  };
  return PDFGradient;
}();
var PDFLinearGradient = /*#__PURE__*/function (_PDFGradient) {
  function PDFLinearGradient(doc, x1, y1, x2, y2) {
    var _this3;
    _this3 = _PDFGradient.call(this, doc) || this;
    _this3.x1 = x1;
    _this3.y1 = y1;
    _this3.x2 = x2;
    _this3.y2 = y2;
    return _this3;
  }
  _inheritsLoose(PDFLinearGradient, _PDFGradient);
  var _proto9 = PDFLinearGradient.prototype;
  _proto9.shader = function shader(fn) {
    return this.doc.ref({
      ShadingType: 2,
      ColorSpace: this._colorSpace,
      Coords: [this.x1, this.y1, this.x2, this.y2],
      Function: fn,
      Extend: [true, true]
    });
  };
  _proto9.opacityGradient = function opacityGradient() {
    return new PDFLinearGradient(this.doc, this.x1, this.y1, this.x2, this.y2);
  };
  return PDFLinearGradient;
}(PDFGradient);
var PDFRadialGradient = /*#__PURE__*/function (_PDFGradient2) {
  function PDFRadialGradient(doc, x1, y1, r1, x2, y2, r2) {
    var _this4;
    _this4 = _PDFGradient2.call(this, doc) || this;
    _this4.doc = doc;
    _this4.x1 = x1;
    _this4.y1 = y1;
    _this4.r1 = r1;
    _this4.x2 = x2;
    _this4.y2 = y2;
    _this4.r2 = r2;
    return _this4;
  }
  _inheritsLoose(PDFRadialGradient, _PDFGradient2);
  var _proto10 = PDFRadialGradient.prototype;
  _proto10.shader = function shader(fn) {
    return this.doc.ref({
      ShadingType: 3,
      ColorSpace: this._colorSpace,
      Coords: [this.x1, this.y1, this.r1, this.x2, this.y2, this.r2],
      Function: fn,
      Extend: [true, true]
    });
  };
  _proto10.opacityGradient = function opacityGradient() {
    return new PDFRadialGradient(this.doc, this.x1, this.y1, this.r1, this.x2, this.y2, this.r2);
  };
  return PDFRadialGradient;
}(PDFGradient);
var Gradient = {
  PDFGradient: PDFGradient,
  PDFLinearGradient: PDFLinearGradient,
  PDFRadialGradient: PDFRadialGradient
};

/*
PDF tiling pattern support. Uncolored only.
 */

var underlyingColorSpaces = ['DeviceCMYK', 'DeviceRGB'];
var PDFTilingPattern = /*#__PURE__*/function () {
  function PDFTilingPattern(doc, bBox, xStep, yStep, stream) {
    this.doc = doc;
    this.bBox = bBox;
    this.xStep = xStep;
    this.yStep = yStep;
    this.stream = stream;
  }
  var _proto11 = PDFTilingPattern.prototype;
  _proto11.createPattern = function createPattern() {
    // no resources needed for our current usage
    // required entry
    var resources = this.doc.ref();
    resources.end();
    // apply default transform matrix (flipped in the default doc._ctm)
    // see document.js & gradient.js
    var _this$doc$_ctm2 = this.doc._ctm,
      m0 = _this$doc$_ctm2[0],
      m1 = _this$doc$_ctm2[1],
      m2 = _this$doc$_ctm2[2],
      m3 = _this$doc$_ctm2[3],
      m4 = _this$doc$_ctm2[4],
      m5 = _this$doc$_ctm2[5];
    var m11 = 1,
      m12 = 0,
      m21 = 0,
      m22 = 1,
      dx = 0,
      dy = 0;
    var m = [m0 * m11 + m2 * m12, m1 * m11 + m3 * m12, m0 * m21 + m2 * m22, m1 * m21 + m3 * m22, m0 * dx + m2 * dy + m4, m1 * dx + m3 * dy + m5];
    var pattern = this.doc.ref({
      Type: 'Pattern',
      PatternType: 1,
      // tiling
      PaintType: 2,
      // 1-colored, 2-uncolored
      TilingType: 2,
      // 2-no distortion
      BBox: this.bBox,
      XStep: this.xStep,
      YStep: this.yStep,
      Matrix: m.map(function (v) {
        return +v.toFixed(5);
      }),
      Resources: resources
    });
    pattern.end(this.stream);
    return pattern;
  };
  _proto11.embedPatternColorSpaces = function embedPatternColorSpaces() {
    var _this5 = this;
    // map each pattern to an underlying color space
    // and embed on each page
    underlyingColorSpaces.forEach(function (csName) {
      var csId = _this5.getPatternColorSpaceId(csName);
      if (_this5.doc.page.colorSpaces[csId]) return;
      var cs = _this5.doc.ref(['Pattern', csName]);
      cs.end();
      _this5.doc.page.colorSpaces[csId] = cs;
    });
  };
  _proto11.getPatternColorSpaceId = function getPatternColorSpaceId(underlyingColorspace) {
    return "CsP" + underlyingColorspace;
  };
  _proto11.embed = function embed() {
    if (!this.id) {
      this.doc._patternCount = this.doc._patternCount + 1;
      this.id = 'P' + this.doc._patternCount;
      this.pattern = this.createPattern();
    }

    // patterns are embedded in each page
    if (!this.doc.page.patterns[this.id]) {
      this.doc.page.patterns[this.id] = this.pattern;
    }
  };
  _proto11.apply = function apply(stroke, patternColor) {
    // do any embedding/creating that might be needed
    this.embedPatternColorSpaces();
    this.embed();
    var normalizedColor = this.doc._normalizeColor(patternColor);
    if (!normalizedColor) throw Error("invalid pattern color. (value: " + patternColor + ")");

    // select one of the pattern color spaces
    var csId = this.getPatternColorSpaceId(this.doc._getColorSpace(normalizedColor));
    this.doc._setColorSpace(csId, stroke);

    // stroke/fill using the pattern and color (in the above underlying color space)
    var op = stroke ? 'SCN' : 'scn';
    return this.doc.addContent(normalizedColor.join(' ') + " /" + this.id + " " + op);
  };
  return PDFTilingPattern;
}();
var pattern = {
  PDFTilingPattern: PDFTilingPattern
};
var PDFGradient$1 = Gradient.PDFGradient,
  PDFLinearGradient$1 = Gradient.PDFLinearGradient,
  PDFRadialGradient$1 = Gradient.PDFRadialGradient;
var PDFTilingPattern$1 = pattern.PDFTilingPattern;
var ColorMixin = {
  initColor: function initColor() {
    this.spotColors = {};
    // The opacity dictionaries
    this._opacityRegistry = {};
    this._opacityCount = 0;
    this._patternCount = 0;
    return this._gradCount = 0;
  },
  _normalizeColor: function _normalizeColor(color) {
    if (typeof color === 'string') {
      if (color.charAt(0) === '#') {
        if (color.length === 4) {
          color = color.replace(/#([0-9A-F])([0-9A-F])([0-9A-F])/i, '#$1$1$2$2$3$3');
        }
        var hex = parseInt(color.slice(1), 16);
        color = [hex >> 16, hex >> 8 & 0xff, hex & 0xff];
      } else if (namedColors[color]) {
        color = namedColors[color];
      } else if (this.spotColors[color]) {
        return this.spotColors[color];
      }
    }
    if (Array.isArray(color)) {
      // RGB
      if (color.length === 3) {
        color = color.map(function (part) {
          return part / 255;
        });
        // CMYK
      } else if (color.length === 4) {
        color = color.map(function (part) {
          return part / 100;
        });
      }
      return color;
    }
    return null;
  },
  _setColor: function _setColor(color, stroke) {
    if (color instanceof PDFGradient$1) {
      color.apply(stroke);
      return true;
      // see if tiling pattern, decode & apply it it
    } else if (Array.isArray(color) && color[0] instanceof PDFTilingPattern$1) {
      color[0].apply(stroke, color[1]);
      return true;
    }
    // any other case should be a normal color and not a pattern
    return this._setColorCore(color, stroke);
  },
  _setColorCore: function _setColorCore(color, stroke) {
    color = this._normalizeColor(color);
    if (!color) {
      return false;
    }
    var op = stroke ? 'SCN' : 'scn';
    var space = this._getColorSpace(color);
    this._setColorSpace(space, stroke);
    if (color instanceof SpotColor) {
      this.page.colorSpaces[color.id] = color.ref;
      this.addContent("1 " + op);
    } else {
      this.addContent(color.join(' ') + " " + op);
    }
    return true;
  },
  _setColorSpace: function _setColorSpace(space, stroke) {
    var op = stroke ? 'CS' : 'cs';
    return this.addContent("/" + space + " " + op);
  },
  _getColorSpace: function _getColorSpace(color) {
    if (color instanceof SpotColor) {
      return color.id;
    }
    return color.length === 4 ? 'DeviceCMYK' : 'DeviceRGB';
  },
  fillColor: function fillColor(color, opacity) {
    var set = this._setColor(color, false);
    if (set) {
      this.fillOpacity(opacity);
    }

    // save this for text wrapper, which needs to reset
    // the fill color on new pages
    this._fillColor = [color, opacity];
    return this;
  },
  strokeColor: function strokeColor(color, opacity) {
    var set = this._setColor(color, true);
    if (set) {
      this.strokeOpacity(opacity);
    }
    return this;
  },
  opacity: function opacity(_opacity) {
    this._doOpacity(_opacity, _opacity);
    return this;
  },
  fillOpacity: function fillOpacity(opacity) {
    this._doOpacity(opacity, null);
    return this;
  },
  strokeOpacity: function strokeOpacity(opacity) {
    this._doOpacity(null, opacity);
    return this;
  },
  _doOpacity: function _doOpacity(fillOpacity, strokeOpacity) {
    var dictionary, name;
    if (fillOpacity == null && strokeOpacity == null) {
      return;
    }
    if (fillOpacity != null) {
      fillOpacity = Math.max(0, Math.min(1, fillOpacity));
    }
    if (strokeOpacity != null) {
      strokeOpacity = Math.max(0, Math.min(1, strokeOpacity));
    }
    var key = fillOpacity + "_" + strokeOpacity;
    if (this._opacityRegistry[key]) {
      var _this$_opacityRegistr = this._opacityRegistry[key];
      dictionary = _this$_opacityRegistr[0];
      name = _this$_opacityRegistr[1];
    } else {
      dictionary = {
        Type: 'ExtGState'
      };
      if (fillOpacity != null) {
        dictionary.ca = fillOpacity;
      }
      if (strokeOpacity != null) {
        dictionary.CA = strokeOpacity;
      }
      dictionary = this.ref(dictionary);
      dictionary.end();
      var id = ++this._opacityCount;
      name = "Gs" + id;
      this._opacityRegistry[key] = [dictionary, name];
    }
    this.page.ext_gstates[name] = dictionary;
    return this.addContent("/" + name + " gs");
  },
  linearGradient: function linearGradient(x1, y1, x2, y2) {
    return new PDFLinearGradient$1(this, x1, y1, x2, y2);
  },
  radialGradient: function radialGradient(x1, y1, r1, x2, y2, r2) {
    return new PDFRadialGradient$1(this, x1, y1, r1, x2, y2, r2);
  },
  pattern: function pattern(bbox, xStep, yStep, stream) {
    return new PDFTilingPattern$1(this, bbox, xStep, yStep, stream);
  },
  addSpotColor: function addSpotColor(name, C, M, Y, K) {
    var color = new SpotColor(this, name, C, M, Y, K);
    this.spotColors[name] = color;
    return this;
  }
};
var namedColors = {
  aliceblue: [240, 248, 255],
  antiquewhite: [250, 235, 215],
  aqua: [0, 255, 255],
  aquamarine: [127, 255, 212],
  azure: [240, 255, 255],
  beige: [245, 245, 220],
  bisque: [255, 228, 196],
  black: [0, 0, 0],
  blanchedalmond: [255, 235, 205],
  blue: [0, 0, 255],
  blueviolet: [138, 43, 226],
  brown: [165, 42, 42],
  burlywood: [222, 184, 135],
  cadetblue: [95, 158, 160],
  chartreuse: [127, 255, 0],
  chocolate: [210, 105, 30],
  coral: [255, 127, 80],
  cornflowerblue: [100, 149, 237],
  cornsilk: [255, 248, 220],
  crimson: [220, 20, 60],
  cyan: [0, 255, 255],
  darkblue: [0, 0, 139],
  darkcyan: [0, 139, 139],
  darkgoldenrod: [184, 134, 11],
  darkgray: [169, 169, 169],
  darkgreen: [0, 100, 0],
  darkgrey: [169, 169, 169],
  darkkhaki: [189, 183, 107],
  darkmagenta: [139, 0, 139],
  darkolivegreen: [85, 107, 47],
  darkorange: [255, 140, 0],
  darkorchid: [153, 50, 204],
  darkred: [139, 0, 0],
  darksalmon: [233, 150, 122],
  darkseagreen: [143, 188, 143],
  darkslateblue: [72, 61, 139],
  darkslategray: [47, 79, 79],
  darkslategrey: [47, 79, 79],
  darkturquoise: [0, 206, 209],
  darkviolet: [148, 0, 211],
  deeppink: [255, 20, 147],
  deepskyblue: [0, 191, 255],
  dimgray: [105, 105, 105],
  dimgrey: [105, 105, 105],
  dodgerblue: [30, 144, 255],
  firebrick: [178, 34, 34],
  floralwhite: [255, 250, 240],
  forestgreen: [34, 139, 34],
  fuchsia: [255, 0, 255],
  gainsboro: [220, 220, 220],
  ghostwhite: [248, 248, 255],
  gold: [255, 215, 0],
  goldenrod: [218, 165, 32],
  gray: [128, 128, 128],
  grey: [128, 128, 128],
  green: [0, 128, 0],
  greenyellow: [173, 255, 47],
  honeydew: [240, 255, 240],
  hotpink: [255, 105, 180],
  indianred: [205, 92, 92],
  indigo: [75, 0, 130],
  ivory: [255, 255, 240],
  khaki: [240, 230, 140],
  lavender: [230, 230, 250],
  lavenderblush: [255, 240, 245],
  lawngreen: [124, 252, 0],
  lemonchiffon: [255, 250, 205],
  lightblue: [173, 216, 230],
  lightcoral: [240, 128, 128],
  lightcyan: [224, 255, 255],
  lightgoldenrodyellow: [250, 250, 210],
  lightgray: [211, 211, 211],
  lightgreen: [144, 238, 144],
  lightgrey: [211, 211, 211],
  lightpink: [255, 182, 193],
  lightsalmon: [255, 160, 122],
  lightseagreen: [32, 178, 170],
  lightskyblue: [135, 206, 250],
  lightslategray: [119, 136, 153],
  lightslategrey: [119, 136, 153],
  lightsteelblue: [176, 196, 222],
  lightyellow: [255, 255, 224],
  lime: [0, 255, 0],
  limegreen: [50, 205, 50],
  linen: [250, 240, 230],
  magenta: [255, 0, 255],
  maroon: [128, 0, 0],
  mediumaquamarine: [102, 205, 170],
  mediumblue: [0, 0, 205],
  mediumorchid: [186, 85, 211],
  mediumpurple: [147, 112, 219],
  mediumseagreen: [60, 179, 113],
  mediumslateblue: [123, 104, 238],
  mediumspringgreen: [0, 250, 154],
  mediumturquoise: [72, 209, 204],
  mediumvioletred: [199, 21, 133],
  midnightblue: [25, 25, 112],
  mintcream: [245, 255, 250],
  mistyrose: [255, 228, 225],
  moccasin: [255, 228, 181],
  navajowhite: [255, 222, 173],
  navy: [0, 0, 128],
  oldlace: [253, 245, 230],
  olive: [128, 128, 0],
  olivedrab: [107, 142, 35],
  orange: [255, 165, 0],
  orangered: [255, 69, 0],
  orchid: [218, 112, 214],
  palegoldenrod: [238, 232, 170],
  palegreen: [152, 251, 152],
  paleturquoise: [175, 238, 238],
  palevioletred: [219, 112, 147],
  papayawhip: [255, 239, 213],
  peachpuff: [255, 218, 185],
  peru: [205, 133, 63],
  pink: [255, 192, 203],
  plum: [221, 160, 221],
  powderblue: [176, 224, 230],
  purple: [128, 0, 128],
  red: [255, 0, 0],
  rosybrown: [188, 143, 143],
  royalblue: [65, 105, 225],
  saddlebrown: [139, 69, 19],
  salmon: [250, 128, 114],
  sandybrown: [244, 164, 96],
  seagreen: [46, 139, 87],
  seashell: [255, 245, 238],
  sienna: [160, 82, 45],
  silver: [192, 192, 192],
  skyblue: [135, 206, 235],
  slateblue: [106, 90, 205],
  slategray: [112, 128, 144],
  slategrey: [112, 128, 144],
  snow: [255, 250, 250],
  springgreen: [0, 255, 127],
  steelblue: [70, 130, 180],
  tan: [210, 180, 140],
  teal: [0, 128, 128],
  thistle: [216, 191, 216],
  tomato: [255, 99, 71],
  turquoise: [64, 224, 208],
  violet: [238, 130, 238],
  wheat: [245, 222, 179],
  white: [255, 255, 255],
  whitesmoke: [245, 245, 245],
  yellow: [255, 255, 0],
  yellowgreen: [154, 205, 50]
};
var cx, cy, px, py, sx, sy;
cx = cy = px = py = sx = sy = 0;
var parameters = {
  A: 7,
  a: 7,
  C: 6,
  c: 6,
  H: 1,
  h: 1,
  L: 2,
  l: 2,
  M: 2,
  m: 2,
  Q: 4,
  q: 4,
  S: 4,
  s: 4,
  T: 2,
  t: 2,
  V: 1,
  v: 1,
  Z: 0,
  z: 0
};
var parse = function parse(path) {
  var cmd;
  var ret = [];
  var args = [];
  var curArg = '';
  var foundDecimal = false;
  var params = 0;
  for (var _iterator3 = _createForOfIteratorHelperLoose(path), _step3; !(_step3 = _iterator3()).done;) {
    var c = _step3.value;
    if (parameters[c] != null) {
      params = parameters[c];
      if (cmd) {
        // save existing command
        if (curArg.length > 0) {
          args[args.length] = +curArg;
        }
        ret[ret.length] = {
          cmd: cmd,
          args: args
        };
        args = [];
        curArg = '';
        foundDecimal = false;
      }
      cmd = c;
    } else if ([' ', ','].includes(c) || c === '-' && curArg.length > 0 && curArg[curArg.length - 1] !== 'e' || c === '.' && foundDecimal) {
      if (curArg.length === 0) {
        continue;
      }
      if (args.length === params) {
        // handle reused commands
        ret[ret.length] = {
          cmd: cmd,
          args: args
        };
        args = [+curArg];

        // handle assumed commands
        if (cmd === 'M') {
          cmd = 'L';
        }
        if (cmd === 'm') {
          cmd = 'l';
        }
      } else {
        args[args.length] = +curArg;
      }
      foundDecimal = c === '.';

      // fix for negative numbers or repeated decimals with no delimeter between commands
      curArg = ['-', '.'].includes(c) ? c : '';
    } else {
      curArg += c;
      if (c === '.') {
        foundDecimal = true;
      }
    }
  }

  // add the last command
  if (curArg.length > 0) {
    if (args.length === params) {
      // handle reused commands
      ret[ret.length] = {
        cmd: cmd,
        args: args
      };
      args = [+curArg];

      // handle assumed commands
      if (cmd === 'M') {
        cmd = 'L';
      }
      if (cmd === 'm') {
        cmd = 'l';
      }
    } else {
      args[args.length] = +curArg;
    }
  }
  ret[ret.length] = {
    cmd: cmd,
    args: args
  };
  return ret;
};
var _apply = function apply(commands, doc) {
  // current point, control point, and subpath starting point
  cx = cy = px = py = sx = sy = 0;

  // run the commands
  for (var i = 0; i < commands.length; i++) {
    var c = commands[i];
    if (typeof runners[c.cmd] === 'function') {
      runners[c.cmd](doc, c.args);
    }
  }
};
var runners = {
  M: function M(doc, a) {
    cx = a[0];
    cy = a[1];
    px = py = null;
    sx = cx;
    sy = cy;
    return doc.moveTo(cx, cy);
  },
  m: function m(doc, a) {
    cx += a[0];
    cy += a[1];
    px = py = null;
    sx = cx;
    sy = cy;
    return doc.moveTo(cx, cy);
  },
  C: function C(doc, a) {
    cx = a[4];
    cy = a[5];
    px = a[2];
    py = a[3];
    return doc.bezierCurveTo.apply(doc, a);
  },
  c: function c(doc, a) {
    doc.bezierCurveTo(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy, a[4] + cx, a[5] + cy);
    px = cx + a[2];
    py = cy + a[3];
    cx += a[4];
    return cy += a[5];
  },
  S: function S(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    }
    doc.bezierCurveTo(cx - (px - cx), cy - (py - cy), a[0], a[1], a[2], a[3]);
    px = a[0];
    py = a[1];
    cx = a[2];
    return cy = a[3];
  },
  s: function s(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    }
    doc.bezierCurveTo(cx - (px - cx), cy - (py - cy), cx + a[0], cy + a[1], cx + a[2], cy + a[3]);
    px = cx + a[0];
    py = cy + a[1];
    cx += a[2];
    return cy += a[3];
  },
  Q: function Q(doc, a) {
    px = a[0];
    py = a[1];
    cx = a[2];
    cy = a[3];
    return doc.quadraticCurveTo(a[0], a[1], cx, cy);
  },
  q: function q(doc, a) {
    doc.quadraticCurveTo(a[0] + cx, a[1] + cy, a[2] + cx, a[3] + cy);
    px = cx + a[0];
    py = cy + a[1];
    cx += a[2];
    return cy += a[3];
  },
  T: function T(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    } else {
      px = cx - (px - cx);
      py = cy - (py - cy);
    }
    doc.quadraticCurveTo(px, py, a[0], a[1]);
    px = cx - (px - cx);
    py = cy - (py - cy);
    cx = a[0];
    return cy = a[1];
  },
  t: function t(doc, a) {
    if (px === null) {
      px = cx;
      py = cy;
    } else {
      px = cx - (px - cx);
      py = cy - (py - cy);
    }
    doc.quadraticCurveTo(px, py, cx + a[0], cy + a[1]);
    cx += a[0];
    return cy += a[1];
  },
  A: function A(doc, a) {
    solveArc(doc, cx, cy, a);
    cx = a[5];
    return cy = a[6];
  },
  a: function a(doc, _a) {
    _a[5] += cx;
    _a[6] += cy;
    solveArc(doc, cx, cy, _a);
    cx = _a[5];
    return cy = _a[6];
  },
  L: function L(doc, a) {
    cx = a[0];
    cy = a[1];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  l: function l(doc, a) {
    cx += a[0];
    cy += a[1];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  H: function H(doc, a) {
    cx = a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  h: function h(doc, a) {
    cx += a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  V: function V(doc, a) {
    cy = a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  v: function v(doc, a) {
    cy += a[0];
    px = py = null;
    return doc.lineTo(cx, cy);
  },
  Z: function Z(doc) {
    doc.closePath();
    cx = sx;
    return cy = sy;
  },
  z: function z(doc) {
    doc.closePath();
    cx = sx;
    return cy = sy;
  }
};
var solveArc = function solveArc(doc, x, y, coords) {
  var rx = coords[0],
    ry = coords[1],
    rot = coords[2],
    large = coords[3],
    sweep = coords[4],
    ex = coords[5],
    ey = coords[6];
  var segs = arcToSegments(ex, ey, rx, ry, large, sweep, rot, x, y);
  for (var _iterator4 = _createForOfIteratorHelperLoose(segs), _step4; !(_step4 = _iterator4()).done;) {
    var seg = _step4.value;
    var bez = segmentToBezier.apply(void 0, seg);
    doc.bezierCurveTo.apply(doc, bez);
  }
};

// from Inkscape svgtopdf, thanks!
var arcToSegments = function arcToSegments(x, y, rx, ry, large, sweep, rotateX, ox, oy) {
  var th = rotateX * (Math.PI / 180);
  var sin_th = Math.sin(th);
  var cos_th = Math.cos(th);
  rx = Math.abs(rx);
  ry = Math.abs(ry);
  px = cos_th * (ox - x) * 0.5 + sin_th * (oy - y) * 0.5;
  py = cos_th * (oy - y) * 0.5 - sin_th * (ox - x) * 0.5;
  var pl = px * px / (rx * rx) + py * py / (ry * ry);
  if (pl > 1) {
    pl = Math.sqrt(pl);
    rx *= pl;
    ry *= pl;
  }
  var a00 = cos_th / rx;
  var a01 = sin_th / rx;
  var a10 = -sin_th / ry;
  var a11 = cos_th / ry;
  var x0 = a00 * ox + a01 * oy;
  var y0 = a10 * ox + a11 * oy;
  var x1 = a00 * x + a01 * y;
  var y1 = a10 * x + a11 * y;
  var d = (x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0);
  var sfactor_sq = 1 / d - 0.25;
  if (sfactor_sq < 0) {
    sfactor_sq = 0;
  }
  var sfactor = Math.sqrt(sfactor_sq);
  if (sweep === large) {
    sfactor = -sfactor;
  }
  var xc = 0.5 * (x0 + x1) - sfactor * (y1 - y0);
  var yc = 0.5 * (y0 + y1) + sfactor * (x1 - x0);
  var th0 = Math.atan2(y0 - yc, x0 - xc);
  var th1 = Math.atan2(y1 - yc, x1 - xc);
  var th_arc = th1 - th0;
  if (th_arc < 0 && sweep === 1) {
    th_arc += 2 * Math.PI;
  } else if (th_arc > 0 && sweep === 0) {
    th_arc -= 2 * Math.PI;
  }
  var segments = Math.ceil(Math.abs(th_arc / (Math.PI * 0.5 + 0.001)));
  var result = [];
  for (var i = 0; i < segments; i++) {
    var th2 = th0 + i * th_arc / segments;
    var th3 = th0 + (i + 1) * th_arc / segments;
    result[i] = [xc, yc, th2, th3, rx, ry, sin_th, cos_th];
  }
  return result;
};
var segmentToBezier = function segmentToBezier(cx, cy, th0, th1, rx, ry, sin_th, cos_th) {
  var a00 = cos_th * rx;
  var a01 = -sin_th * ry;
  var a10 = sin_th * rx;
  var a11 = cos_th * ry;
  var th_half = 0.5 * (th1 - th0);
  var t = 8 / 3 * Math.sin(th_half * 0.5) * Math.sin(th_half * 0.5) / Math.sin(th_half);
  var x1 = cx + Math.cos(th0) - t * Math.sin(th0);
  var y1 = cy + Math.sin(th0) + t * Math.cos(th0);
  var x3 = cx + Math.cos(th1);
  var y3 = cy + Math.sin(th1);
  var x2 = x3 + t * Math.sin(th1);
  var y2 = y3 - t * Math.cos(th1);
  return [a00 * x1 + a01 * y1, a10 * x1 + a11 * y1, a00 * x2 + a01 * y2, a10 * x2 + a11 * y2, a00 * x3 + a01 * y3, a10 * x3 + a11 * y3];
};
var SVGPath = /*#__PURE__*/function () {
  function SVGPath() {}
  SVGPath.apply = function apply(doc, path) {
    var commands = parse(path);
    _apply(commands, doc);
  };
  return SVGPath;
}();
var number$1 = PDFObject.number;

// This constant is used to approximate a symmetrical arc using a cubic
// Bezier curve.
var KAPPA = 4.0 * ((Math.sqrt(2) - 1.0) / 3.0);
var VectorMixin = {
  initVector: function initVector() {
    this._ctm = [1, 0, 0, 1, 0, 0]; // current transformation matrix
    return this._ctmStack = [];
  },
  save: function save() {
    this._ctmStack.push(this._ctm.slice());
    // TODO: save/restore colorspace and styles so not setting it unnessesarily all the time?
    return this.addContent('q');
  },
  restore: function restore() {
    this._ctm = this._ctmStack.pop() || [1, 0, 0, 1, 0, 0];
    return this.addContent('Q');
  },
  closePath: function closePath() {
    return this.addContent('h');
  },
  lineWidth: function lineWidth(w) {
    return this.addContent(number$1(w) + " w");
  },
  _CAP_STYLES: {
    BUTT: 0,
    ROUND: 1,
    SQUARE: 2
  },
  lineCap: function lineCap(c) {
    if (typeof c === 'string') {
      c = this._CAP_STYLES[c.toUpperCase()];
    }
    return this.addContent(c + " J");
  },
  _JOIN_STYLES: {
    MITER: 0,
    ROUND: 1,
    BEVEL: 2
  },
  lineJoin: function lineJoin(j) {
    if (typeof j === 'string') {
      j = this._JOIN_STYLES[j.toUpperCase()];
    }
    return this.addContent(j + " j");
  },
  miterLimit: function miterLimit(m) {
    return this.addContent(number$1(m) + " M");
  },
  dash: function dash(length) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var originalLength = length;
    if (!Array.isArray(length)) {
      length = [length, options.space || length];
    }
    var valid = length.every(function (x) {
      return Number.isFinite(x) && x > 0;
    });
    if (!valid) {
      throw new Error("dash(" + JSON.stringify(originalLength) + ", " + JSON.stringify(options) + ") invalid, lengths must be numeric and greater than zero");
    }
    length = length.map(number$1).join(' ');
    return this.addContent("[" + length + "] " + number$1(options.phase || 0) + " d");
  },
  undash: function undash() {
    return this.addContent('[] 0 d');
  },
  moveTo: function moveTo(x, y) {
    return this.addContent(number$1(x) + " " + number$1(y) + " m");
  },
  lineTo: function lineTo(x, y) {
    return this.addContent(number$1(x) + " " + number$1(y) + " l");
  },
  bezierCurveTo: function bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y) {
    return this.addContent(number$1(cp1x) + " " + number$1(cp1y) + " " + number$1(cp2x) + " " + number$1(cp2y) + " " + number$1(x) + " " + number$1(y) + " c");
  },
  quadraticCurveTo: function quadraticCurveTo(cpx, cpy, x, y) {
    return this.addContent(number$1(cpx) + " " + number$1(cpy) + " " + number$1(x) + " " + number$1(y) + " v");
  },
  rect: function rect(x, y, w, h) {
    return this.addContent(number$1(x) + " " + number$1(y) + " " + number$1(w) + " " + number$1(h) + " re");
  },
  roundedRect: function roundedRect(x, y, w, h, r) {
    if (r == null) {
      r = 0;
    }
    r = Math.min(r, 0.5 * w, 0.5 * h);

    // amount to inset control points from corners (see `ellipse`)
    var c = r * (1.0 - KAPPA);
    this.moveTo(x + r, y);
    this.lineTo(x + w - r, y);
    this.bezierCurveTo(x + w - c, y, x + w, y + c, x + w, y + r);
    this.lineTo(x + w, y + h - r);
    this.bezierCurveTo(x + w, y + h - c, x + w - c, y + h, x + w - r, y + h);
    this.lineTo(x + r, y + h);
    this.bezierCurveTo(x + c, y + h, x, y + h - c, x, y + h - r);
    this.lineTo(x, y + r);
    this.bezierCurveTo(x, y + c, x + c, y, x + r, y);
    return this.closePath();
  },
  ellipse: function ellipse(x, y, r1, r2) {
    // based on http://stackoverflow.com/questions/2172798/how-to-draw-an-oval-in-html5-canvas/2173084#2173084
    if (r2 == null) {
      r2 = r1;
    }
    x -= r1;
    y -= r2;
    var ox = r1 * KAPPA;
    var oy = r2 * KAPPA;
    var xe = x + r1 * 2;
    var ye = y + r2 * 2;
    var xm = x + r1;
    var ym = y + r2;
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    return this.closePath();
  },
  circle: function circle(x, y, radius) {
    return this.ellipse(x, y, radius);
  },
  arc: function arc(x, y, radius, startAngle, endAngle, anticlockwise) {
    if (anticlockwise == null) {
      anticlockwise = false;
    }
    var TWO_PI = 2.0 * Math.PI;
    var HALF_PI = 0.5 * Math.PI;
    var deltaAng = endAngle - startAngle;
    if (Math.abs(deltaAng) > TWO_PI) {
      // draw only full circle if more than that is specified
      deltaAng = TWO_PI;
    } else if (deltaAng !== 0 && anticlockwise !== deltaAng < 0) {
      // necessary to flip direction of rendering
      var dir = anticlockwise ? -1 : 1;
      deltaAng = dir * TWO_PI + deltaAng;
    }
    var numSegs = Math.ceil(Math.abs(deltaAng) / HALF_PI);
    var segAng = deltaAng / numSegs;
    var handleLen = segAng / HALF_PI * KAPPA * radius;
    var curAng = startAngle;

    // component distances between anchor point and control point
    var deltaCx = -Math.sin(curAng) * handleLen;
    var deltaCy = Math.cos(curAng) * handleLen;

    // anchor point
    var ax = x + Math.cos(curAng) * radius;
    var ay = y + Math.sin(curAng) * radius;

    // calculate and render segments
    this.moveTo(ax, ay);
    for (var segIdx = 0; segIdx < numSegs; segIdx++) {
      // starting control point
      var cp1x = ax + deltaCx;
      var cp1y = ay + deltaCy;

      // step angle
      curAng += segAng;

      // next anchor point
      ax = x + Math.cos(curAng) * radius;
      ay = y + Math.sin(curAng) * radius;

      // next control point delta
      deltaCx = -Math.sin(curAng) * handleLen;
      deltaCy = Math.cos(curAng) * handleLen;

      // ending control point
      var cp2x = ax - deltaCx;
      var cp2y = ay - deltaCy;

      // render segment
      this.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ax, ay);
    }
    return this;
  },
  polygon: function polygon() {
    for (var _len = arguments.length, points = new Array(_len), _key = 0; _key < _len; _key++) {
      points[_key] = arguments[_key];
    }
    this.moveTo.apply(this, points.shift() || []);
    for (var _i3 = 0, _points = points; _i3 < _points.length; _i3++) {
      var point = _points[_i3];
      this.lineTo.apply(this, point || []);
    }
    return this.closePath();
  },
  path: function path(_path) {
    SVGPath.apply(this, _path);
    return this;
  },
  _windingRule: function _windingRule(rule) {
    if (/even-?odd/.test(rule)) {
      return '*';
    }
    return '';
  },
  fill: function fill(color, rule) {
    if (/(even-?odd)|(non-?zero)/.test(color)) {
      rule = color;
      color = null;
    }
    if (color) {
      this.fillColor(color);
    }
    return this.addContent("f" + this._windingRule(rule));
  },
  stroke: function stroke(color) {
    if (color) {
      this.strokeColor(color);
    }
    return this.addContent('S');
  },
  fillAndStroke: function fillAndStroke(fillColor, strokeColor, rule) {
    if (strokeColor == null) {
      strokeColor = fillColor;
    }
    var isFillRule = /(even-?odd)|(non-?zero)/;
    if (isFillRule.test(fillColor)) {
      rule = fillColor;
      fillColor = null;
    }
    if (isFillRule.test(strokeColor)) {
      rule = strokeColor;
      strokeColor = fillColor;
    }
    if (fillColor) {
      this.fillColor(fillColor);
      this.strokeColor(strokeColor);
    }
    return this.addContent("B" + this._windingRule(rule));
  },
  clip: function clip(rule) {
    return this.addContent("W" + this._windingRule(rule) + " n");
  },
  transform: function transform(m11, m12, m21, m22, dx, dy) {
    // keep track of the current transformation matrix
    if (m11 === 1 && m12 === 0 && m21 === 0 && m22 === 1 && dx === 0 && dy === 0) {
      // Ignore identity transforms
      return this;
    }
    var m = this._ctm;
    var m0 = m[0],
      m1 = m[1],
      m2 = m[2],
      m3 = m[3],
      m4 = m[4],
      m5 = m[5];
    m[0] = m0 * m11 + m2 * m12;
    m[1] = m1 * m11 + m3 * m12;
    m[2] = m0 * m21 + m2 * m22;
    m[3] = m1 * m21 + m3 * m22;
    m[4] = m0 * dx + m2 * dy + m4;
    m[5] = m1 * dx + m3 * dy + m5;
    var values = [m11, m12, m21, m22, dx, dy].map(function (v) {
      return number$1(v);
    }).join(' ');
    return this.addContent(values + " cm");
  },
  translate: function translate(x, y) {
    return this.transform(1, 0, 0, 1, x, y);
  },
  rotate: function rotate(angle) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var y;
    var rad = angle * Math.PI / 180;
    var cos = Math.cos(rad);
    var sin = Math.sin(rad);
    var x = y = 0;
    if (options.origin != null) {
      var _options$origin = options.origin;
      x = _options$origin[0];
      y = _options$origin[1];
      var x1 = x * cos - y * sin;
      var y1 = x * sin + y * cos;
      x -= x1;
      y -= y1;
    }
    return this.transform(cos, sin, -sin, cos, x, y);
  },
  scale: function scale(xFactor, yFactor) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var y;
    if (yFactor == null) {
      yFactor = xFactor;
    }
    if (typeof yFactor === 'object') {
      options = yFactor;
      yFactor = xFactor;
    }
    var x = y = 0;
    if (options.origin != null) {
      var _options$origin2 = options.origin;
      x = _options$origin2[0];
      y = _options$origin2[1];
      x -= xFactor * x;
      y -= yFactor * y;
    }
    return this.transform(xFactor, 0, 0, yFactor, x, y);
  }
};
var WIN_ANSI_MAP = {
  402: 131,
  8211: 150,
  8212: 151,
  8216: 145,
  8217: 146,
  8218: 130,
  8220: 147,
  8221: 148,
  8222: 132,
  8224: 134,
  8225: 135,
  8226: 149,
  8230: 133,
  8364: 128,
  8240: 137,
  8249: 139,
  8250: 155,
  710: 136,
  8482: 153,
  338: 140,
  339: 156,
  732: 152,
  352: 138,
  353: 154,
  376: 159,
  381: 142,
  382: 158
};
var characters = ".notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n.notdef       .notdef        .notdef        .notdef\n  \nspace         exclam         quotedbl       numbersign\ndollar        percent        ampersand      quotesingle\nparenleft     parenright     asterisk       plus\ncomma         hyphen         period         slash\nzero          one            two            three\nfour          five           six            seven\neight         nine           colon          semicolon\nless          equal          greater        question\n  \nat            A              B              C\nD             E              F              G\nH             I              J              K\nL             M              N              O\nP             Q              R              S\nT             U              V              W\nX             Y              Z              bracketleft\nbackslash     bracketright   asciicircum    underscore\n  \ngrave         a              b              c\nd             e              f              g\nh             i              j              k\nl             m              n              o\np             q              r              s\nt             u              v              w\nx             y              z              braceleft\nbar           braceright     asciitilde     .notdef\n  \nEuro          .notdef        quotesinglbase florin\nquotedblbase  ellipsis       dagger         daggerdbl\ncircumflex    perthousand    Scaron         guilsinglleft\nOE            .notdef        Zcaron         .notdef\n.notdef       quoteleft      quoteright     quotedblleft\nquotedblright bullet         endash         emdash\ntilde         trademark      scaron         guilsinglright\noe            .notdef        zcaron         ydieresis\n  \nspace         exclamdown     cent           sterling\ncurrency      yen            brokenbar      section\ndieresis      copyright      ordfeminine    guillemotleft\nlogicalnot    hyphen         registered     macron\ndegree        plusminus      twosuperior    threesuperior\nacute         mu             paragraph      periodcentered\ncedilla       onesuperior    ordmasculine   guillemotright\nonequarter    onehalf        threequarters  questiondown\n  \nAgrave        Aacute         Acircumflex    Atilde\nAdieresis     Aring          AE             Ccedilla\nEgrave        Eacute         Ecircumflex    Edieresis\nIgrave        Iacute         Icircumflex    Idieresis\nEth           Ntilde         Ograve         Oacute\nOcircumflex   Otilde         Odieresis      multiply\nOslash        Ugrave         Uacute         Ucircumflex\nUdieresis     Yacute         Thorn          germandbls\n  \nagrave        aacute         acircumflex    atilde\nadieresis     aring          ae             ccedilla\negrave        eacute         ecircumflex    edieresis\nigrave        iacute         icircumflex    idieresis\neth           ntilde         ograve         oacute\nocircumflex   otilde         odieresis      divide\noslash        ugrave         uacute         ucircumflex\nudieresis     yacute         thorn          ydieresis".split(/\s+/);
var AFMFont = /*#__PURE__*/function () {
  function AFMFont(contents) {
    this.contents = contents;
    this.attributes = {};
    this.glyphWidths = {};
    this.boundingBoxes = {};
    this.kernPairs = {};
    this.parse();
    // todo: remove charWidths since appears to not be used
    this.charWidths = new Array(256);
    for (var char = 0; char <= 255; char++) {
      this.charWidths[char] = this.glyphWidths[characters[char]];
    }
    this.bbox = this.attributes['FontBBox'].split(/\s+/).map(function (e) {
      return +e;
    });
    this.ascender = +(this.attributes['Ascender'] || 0);
    this.descender = +(this.attributes['Descender'] || 0);
    this.xHeight = +(this.attributes['XHeight'] || 0);
    this.capHeight = +(this.attributes['CapHeight'] || 0);
    this.lineGap = this.bbox[3] - this.bbox[1] - (this.ascender - this.descender);
  }
  AFMFont.open = function open(filename) {
    return new AFMFont(fs.readFileSync(filename, 'utf8'));
  };
  var _proto12 = AFMFont.prototype;
  _proto12.parse = function parse() {
    var section = '';
    for (var _iterator5 = _createForOfIteratorHelperLoose(this.contents.split('\n')), _step5; !(_step5 = _iterator5()).done;) {
      var line = _step5.value;
      var match;
      var a;
      if (match = line.match(/^Start(\w+)/)) {
        section = match[1];
        continue;
      } else if (match = line.match(/^End(\w+)/)) {
        section = '';
        continue;
      }
      switch (section) {
        case 'FontMetrics':
          match = line.match(/(^\w+)\s+(.*)/);
          var key = match[1];
          var value = match[2];
          if (a = this.attributes[key]) {
            if (!Array.isArray(a)) {
              a = this.attributes[key] = [a];
            }
            a.push(value);
          } else {
            this.attributes[key] = value;
          }
          break;
        case 'CharMetrics':
          if (!/^CH?\s/.test(line)) {
            continue;
          }
          var name = line.match(/\bN\s+(\.?\w+)\s*;/)[1];
          this.glyphWidths[name] = +line.match(/\bWX\s+(\d+)\s*;/)[1];
          break;
        case 'KernPairs':
          match = line.match(/^KPX\s+(\.?\w+)\s+(\.?\w+)\s+(-?\d+)/);
          if (match) {
            this.kernPairs[match[1] + '\0' + match[2]] = parseInt(match[3]);
          }
          break;
      }
    }
  };
  _proto12.encodeText = function encodeText(text) {
    var res = [];
    for (var i = 0, len = text.length; i < len; i++) {
      var char = text.charCodeAt(i);
      char = WIN_ANSI_MAP[char] || char;
      res.push(char.toString(16));
    }
    return res;
  };
  _proto12.glyphsForString = function glyphsForString(string) {
    var glyphs = [];
    for (var i = 0, len = string.length; i < len; i++) {
      var charCode = string.charCodeAt(i);
      glyphs.push(this.characterToGlyph(charCode));
    }
    return glyphs;
  };
  _proto12.characterToGlyph = function characterToGlyph(character) {
    return characters[WIN_ANSI_MAP[character] || character] || '.notdef';
  };
  _proto12.widthOfGlyph = function widthOfGlyph(glyph) {
    return this.glyphWidths[glyph] || 0;
  };
  _proto12.getKernPair = function getKernPair(left, right) {
    return this.kernPairs[left + '\0' + right] || 0;
  };
  _proto12.advancesForGlyphs = function advancesForGlyphs(glyphs) {
    var advances = [];
    for (var index = 0; index < glyphs.length; index++) {
      var left = glyphs[index];
      var right = glyphs[index + 1];
      advances.push(this.widthOfGlyph(left) + this.getKernPair(left, right));
    }
    return advances;
  };
  return AFMFont;
}();
var PDFFont = /*#__PURE__*/function () {
  function PDFFont() {}
  var _proto13 = PDFFont.prototype;
  _proto13.encode = function encode() {
    throw new Error('Must be implemented by subclasses');
  };
  _proto13.widthOfString = function widthOfString() {
    throw new Error('Must be implemented by subclasses');
  };
  _proto13.ref = function ref() {
    return this.dictionary != null ? this.dictionary : this.dictionary = this.document.ref();
  };
  _proto13.finalize = function finalize() {
    if (this.embedded || this.dictionary == null) {
      return;
    }
    this.embed();
    return this.embedded = true;
  };
  _proto13.embed = function embed() {
    throw new Error('Must be implemented by subclasses');
  };
  _proto13.lineHeight = function lineHeight(size, includeGap) {
    if (includeGap == null) {
      includeGap = false;
    }
    var gap = includeGap ? this.lineGap : 0;
    return (this.ascender + gap - this.descender) / 1000 * size;
  };
  return PDFFont;
}(); // This insanity is so bundlers can inline the font files
var STANDARD_FONTS = {
  Courier: function Courier() {
    return fs.readFileSync(__dirname + '/data/Courier.afm', 'utf8');
  },
  'Courier-Bold': function CourierBold() {
    return fs.readFileSync(__dirname + '/data/Courier-Bold.afm', 'utf8');
  },
  'Courier-Oblique': function CourierOblique() {
    return fs.readFileSync(__dirname + '/data/Courier-Oblique.afm', 'utf8');
  },
  'Courier-BoldOblique': function CourierBoldOblique() {
    return fs.readFileSync(__dirname + '/data/Courier-BoldOblique.afm', 'utf8');
  },
  Helvetica: function Helvetica() {
    return fs.readFileSync(__dirname + '/data/Helvetica.afm', 'utf8');
  },
  'Helvetica-Bold': function HelveticaBold() {
    return fs.readFileSync(__dirname + '/data/Helvetica-Bold.afm', 'utf8');
  },
  'Helvetica-Oblique': function HelveticaOblique() {
    return fs.readFileSync(__dirname + '/data/Helvetica-Oblique.afm', 'utf8');
  },
  'Helvetica-BoldOblique': function HelveticaBoldOblique() {
    return fs.readFileSync(__dirname + '/data/Helvetica-BoldOblique.afm', 'utf8');
  },
  'Times-Roman': function TimesRoman() {
    return fs.readFileSync(__dirname + '/data/Times-Roman.afm', 'utf8');
  },
  'Times-Bold': function TimesBold() {
    return fs.readFileSync(__dirname + '/data/Times-Bold.afm', 'utf8');
  },
  'Times-Italic': function TimesItalic() {
    return fs.readFileSync(__dirname + '/data/Times-Italic.afm', 'utf8');
  },
  'Times-BoldItalic': function TimesBoldItalic() {
    return fs.readFileSync(__dirname + '/data/Times-BoldItalic.afm', 'utf8');
  },
  Symbol: function Symbol() {
    return fs.readFileSync(__dirname + '/data/Symbol.afm', 'utf8');
  },
  ZapfDingbats: function ZapfDingbats() {
    return fs.readFileSync(__dirname + '/data/ZapfDingbats.afm', 'utf8');
  }
};
var StandardFont = /*#__PURE__*/function (_PDFFont) {
  function StandardFont(document, name, id) {
    var _this6;
    _this6 = _PDFFont.call(this) || this;
    _this6.document = document;
    _this6.name = name;
    _this6.id = id;
    _this6.font = new AFMFont(STANDARD_FONTS[_this6.name]());
    var _this6$font = _this6.font;
    _this6.ascender = _this6$font.ascender;
    _this6.descender = _this6$font.descender;
    _this6.bbox = _this6$font.bbox;
    _this6.lineGap = _this6$font.lineGap;
    _this6.xHeight = _this6$font.xHeight;
    _this6.capHeight = _this6$font.capHeight;
    return _this6;
  }
  _inheritsLoose(StandardFont, _PDFFont);
  var _proto14 = StandardFont.prototype;
  _proto14.embed = function embed() {
    this.dictionary.data = {
      Type: 'Font',
      BaseFont: this.name,
      Subtype: 'Type1',
      Encoding: 'WinAnsiEncoding'
    };
    return this.dictionary.end();
  };
  _proto14.encode = function encode(text) {
    var encoded = this.font.encodeText(text);
    var glyphs = this.font.glyphsForString("" + text);
    var advances = this.font.advancesForGlyphs(glyphs);
    var positions = [];
    for (var i = 0; i < glyphs.length; i++) {
      var glyph = glyphs[i];
      positions.push({
        xAdvance: advances[i],
        yAdvance: 0,
        xOffset: 0,
        yOffset: 0,
        advanceWidth: this.font.widthOfGlyph(glyph)
      });
    }
    return [encoded, positions];
  };
  _proto14.widthOfString = function widthOfString(string, size) {
    var glyphs = this.font.glyphsForString("" + string);
    var advances = this.font.advancesForGlyphs(glyphs);
    var width = 0;
    for (var _iterator6 = _createForOfIteratorHelperLoose(advances), _step6; !(_step6 = _iterator6()).done;) {
      var advance = _step6.value;
      width += advance;
    }
    var scale = size / 1000;
    return width * scale;
  };
  StandardFont.isStandardFont = function isStandardFont(name) {
    return name in STANDARD_FONTS;
  };
  return StandardFont;
}(PDFFont);
var toHex = function toHex(num) {
  return ("0000" + num.toString(16)).slice(-4);
};
var EmbeddedFont = /*#__PURE__*/function (_PDFFont2) {
  function EmbeddedFont(document, font, id) {
    var _this7;
    _this7 = _PDFFont2.call(this) || this;
    _this7.document = document;
    _this7.font = font;
    _this7.id = id;
    _this7.subset = _this7.font.createSubset();
    _this7.unicode = [[0]];
    _this7.widths = [_this7.font.getGlyph(0).advanceWidth];
    _this7.name = _this7.font.postscriptName;
    _this7.scale = 1000 / _this7.font.unitsPerEm;
    _this7.ascender = _this7.font.ascent * _this7.scale;
    _this7.descender = _this7.font.descent * _this7.scale;
    _this7.xHeight = _this7.font.xHeight * _this7.scale;
    _this7.capHeight = _this7.font.capHeight * _this7.scale;
    _this7.lineGap = _this7.font.lineGap * _this7.scale;
    _this7.bbox = _this7.font.bbox;
    if (document.options.fontLayoutCache !== false) {
      _this7.layoutCache = Object.create(null);
    }
    return _this7;
  }
  _inheritsLoose(EmbeddedFont, _PDFFont2);
  var _proto15 = EmbeddedFont.prototype;
  _proto15.layoutRun = function layoutRun(text, features) {
    var run = this.font.layout(text, features);

    // Normalize position values
    for (var i = 0; i < run.positions.length; i++) {
      var position = run.positions[i];
      for (var key in position) {
        position[key] *= this.scale;
      }
      position.advanceWidth = run.glyphs[i].advanceWidth * this.scale;
    }
    return run;
  };
  _proto15.layoutCached = function layoutCached(text) {
    if (!this.layoutCache) {
      return this.layoutRun(text);
    }
    var cached;
    if (cached = this.layoutCache[text]) {
      return cached;
    }
    var run = this.layoutRun(text);
    this.layoutCache[text] = run;
    return run;
  };
  _proto15.layout = function layout(text, features, onlyWidth) {
    // Skip the cache if any user defined features are applied
    if (features) {
      return this.layoutRun(text, features);
    }
    var glyphs = onlyWidth ? null : [];
    var positions = onlyWidth ? null : [];
    var advanceWidth = 0;

    // Split the string by words to increase cache efficiency.
    // For this purpose, spaces and tabs are a good enough delimeter.
    var last = 0;
    var index = 0;
    while (index <= text.length) {
      var needle;
      if (index === text.length && last < index || (needle = text.charAt(index), [' ', '\t'].includes(needle))) {
        var run = this.layoutCached(text.slice(last, ++index));
        if (!onlyWidth) {
          glyphs = glyphs.concat(run.glyphs);
          positions = positions.concat(run.positions);
        }
        advanceWidth += run.advanceWidth;
        last = index;
      } else {
        index++;
      }
    }
    return {
      glyphs: glyphs,
      positions: positions,
      advanceWidth: advanceWidth
    };
  };
  _proto15.encode = function encode(text, features) {
    var _this$layout = this.layout(text, features),
      glyphs = _this$layout.glyphs,
      positions = _this$layout.positions;
    var res = [];
    for (var i = 0; i < glyphs.length; i++) {
      var glyph = glyphs[i];
      var gid = this.subset.includeGlyph(glyph.id);
      res.push(("0000" + gid.toString(16)).slice(-4));
      if (this.widths[gid] == null) {
        this.widths[gid] = glyph.advanceWidth * this.scale;
      }
      if (this.unicode[gid] == null) {
        this.unicode[gid] = glyph.codePoints;
      }
    }
    return [res, positions];
  };
  _proto15.widthOfString = function widthOfString(string, size, features) {
    var width = this.layout(string, features, true).advanceWidth;
    var scale = size / 1000;
    return width * scale;
  };
  _proto15.embed = function embed() {
    var _this8 = this;
    var isCFF = this.subset.cff != null;
    var fontFile = this.document.ref();
    if (isCFF) {
      fontFile.data.Subtype = 'CIDFontType0C';
    }
    this.subset.encodeStream().on('data', function (data) {
      return fontFile.write(data);
    }).on('end', function () {
      return fontFile.end();
    });
    var familyClass = ((this.font['OS/2'] != null ? this.font['OS/2'].sFamilyClass : undefined) || 0) >> 8;
    var flags = 0;
    if (this.font.post.isFixedPitch) {
      flags |= 1 << 0;
    }
    if (1 <= familyClass && familyClass <= 7) {
      flags |= 1 << 1;
    }
    flags |= 1 << 2; // assume the font uses non-latin characters
    if (familyClass === 10) {
      flags |= 1 << 3;
    }
    if (this.font.head.macStyle.italic) {
      flags |= 1 << 6;
    }

    // generate a tag (6 uppercase letters. 17 is the char code offset from '0' to 'A'. 73 will map to 'Z')
    var tag = [1, 2, 3, 4, 5, 6].map(function (i) {
      return String.fromCharCode((_this8.id.charCodeAt(i) || 73) + 17);
    }).join('');
    var name = tag + '+' + this.font.postscriptName.replaceAll(' ', '_');
    var bbox = this.font.bbox;
    var descriptor = this.document.ref({
      Type: 'FontDescriptor',
      FontName: name,
      Flags: flags,
      FontBBox: [bbox.minX * this.scale, bbox.minY * this.scale, bbox.maxX * this.scale, bbox.maxY * this.scale],
      ItalicAngle: this.font.italicAngle,
      Ascent: this.ascender,
      Descent: this.descender,
      CapHeight: (this.font.capHeight || this.font.ascent) * this.scale,
      XHeight: (this.font.xHeight || 0) * this.scale,
      StemV: 0
    }); // not sure how to calculate this

    if (isCFF) {
      descriptor.data.FontFile3 = fontFile;
    } else {
      descriptor.data.FontFile2 = fontFile;
    }
    if (this.document.subset && this.document.subset === 1) {
      var CIDSet = Buffer.from('FFFFFFFFC0', 'hex');
      var CIDSetRef = this.document.ref();
      CIDSetRef.write(CIDSet);
      CIDSetRef.end();
      descriptor.data.CIDSet = CIDSetRef;
    }
    descriptor.end();
    var descendantFontData = {
      Type: 'Font',
      Subtype: 'CIDFontType0',
      BaseFont: name,
      CIDSystemInfo: {
        Registry: new String('Adobe'),
        Ordering: new String('Identity'),
        Supplement: 0
      },
      FontDescriptor: descriptor,
      W: [0, this.widths]
    };
    if (!isCFF) {
      descendantFontData.Subtype = 'CIDFontType2';
      descendantFontData.CIDToGIDMap = 'Identity';
    }
    var descendantFont = this.document.ref(descendantFontData);
    descendantFont.end();
    this.dictionary.data = {
      Type: 'Font',
      Subtype: 'Type0',
      BaseFont: name,
      Encoding: 'Identity-H',
      DescendantFonts: [descendantFont],
      ToUnicode: this.toUnicodeCmap()
    };
    return this.dictionary.end();
  }

  // Maps the glyph ids encoded in the PDF back to unicode strings
  // Because of ligature substitutions and the like, there may be one or more
  // unicode characters represented by each glyph.
  ;
  _proto15.toUnicodeCmap = function toUnicodeCmap() {
    var cmap = this.document.ref();
    var entries = [];
    for (var _iterator7 = _createForOfIteratorHelperLoose(this.unicode), _step7; !(_step7 = _iterator7()).done;) {
      var codePoints = _step7.value;
      var encoded = [];

      // encode codePoints to utf16
      for (var _iterator8 = _createForOfIteratorHelperLoose(codePoints), _step8; !(_step8 = _iterator8()).done;) {
        var value = _step8.value;
        if (value > 0xffff) {
          value -= 0x10000;
          encoded.push(toHex(value >>> 10 & 0x3ff | 0xd800));
          value = 0xdc00 | value & 0x3ff;
        }
        encoded.push(toHex(value));
      }
      entries.push("<" + encoded.join(' ') + ">");
    }
    var chunkSize = 256;
    var chunks = Math.ceil(entries.length / chunkSize);
    var ranges = [];
    for (var i = 0; i < chunks; i++) {
      var start = i * chunkSize;
      var end = Math.min((i + 1) * chunkSize, entries.length);
      ranges.push("<" + toHex(start) + "> <" + toHex(end - 1) + "> [" + entries.slice(start, end).join(' ') + "]");
    }
    cmap.end("/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n/CIDSystemInfo <<\n  /Registry (Adobe)\n  /Ordering (UCS)\n  /Supplement 0\n>> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n1 begincodespacerange\n<0000><ffff>\nendcodespacerange\n1 beginbfrange\n" + ranges.join('\n') + "\nendbfrange\nendcmap\nCMapName currentdict /CMap defineresource pop\nend\nend");
    return cmap;
  };
  return EmbeddedFont;
}(PDFFont);
var PDFFontFactory = /*#__PURE__*/function () {
  function PDFFontFactory() {}
  PDFFontFactory.open = function open(document, src, family, id) {
    var font;
    if (typeof src === 'string') {
      if (StandardFont.isStandardFont(src)) {
        return new StandardFont(document, src, id);
      }
      src = fs.readFileSync(src);
    }
    if (Buffer.isBuffer(src)) {
      font = _fontkit.default.create(src, family);
    } else if (src instanceof Uint8Array) {
      font = _fontkit.default.create(Buffer.from(src), family);
    } else if (src instanceof ArrayBuffer) {
      font = _fontkit.default.create(Buffer.from(new Uint8Array(src)), family);
    }
    if (font == null) {
      throw new Error('Not a supported font format or standard PDF font.');
    }
    return new EmbeddedFont(document, font, id);
  };
  return PDFFontFactory;
}();
var isEqualFont = function isEqualFont(font1, font2) {
  var _font1$font$_tables, _font2$font$_tables, _font1$font$_tables2, _font2$font$_tables2;
  // compare font checksum
  if (((_font1$font$_tables = font1.font._tables) == null || (_font1$font$_tables = _font1$font$_tables.head) == null ? void 0 : _font1$font$_tables.checkSumAdjustment) !== ((_font2$font$_tables = font2.font._tables) == null || (_font2$font$_tables = _font2$font$_tables.head) == null ? void 0 : _font2$font$_tables.checkSumAdjustment)) {
    return false;
  }

  // compare font name table
  if (JSON.stringify((_font1$font$_tables2 = font1.font._tables) == null || (_font1$font$_tables2 = _font1$font$_tables2.name) == null ? void 0 : _font1$font$_tables2.records) !== JSON.stringify((_font2$font$_tables2 = font2.font._tables) == null || (_font2$font$_tables2 = _font2$font$_tables2.name) == null ? void 0 : _font2$font$_tables2.records)) {
    return false;
  }
  return true;
};
var FontsMixin = {
  initFonts: function initFonts() {
    var defaultFont = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'Helvetica';
    // Lookup table for embedded fonts
    this._fontFamilies = {};
    this._fontCount = 0;

    // Font state
    this._fontSize = 12;
    this._font = null;
    this._registeredFonts = {};

    // Set the default font
    if (defaultFont) {
      this.font(defaultFont);
    }
  },
  font: function font(src, family, size) {
    var cacheKey, font;
    if (typeof family === 'number') {
      size = family;
      family = null;
    }

    // check registered fonts if src is a string
    if (typeof src === 'string' && this._registeredFonts[src]) {
      cacheKey = src;
      var _this$_registeredFont = this._registeredFonts[src];
      src = _this$_registeredFont.src;
      family = _this$_registeredFont.family;
    } else {
      cacheKey = family || src;
      if (typeof cacheKey !== 'string') {
        cacheKey = null;
      }
    }
    if (size != null) {
      this.fontSize(size);
    }

    // fast path: check if the font is already in the PDF
    if (font = this._fontFamilies[cacheKey]) {
      this._font = font;
      return this;
    }

    // load the font
    var id = "F" + ++this._fontCount;
    this._font = PDFFontFactory.open(this, src, family, id);

    // check for existing font familes with the same name already in the PDF
    // useful if the font was passed as a buffer
    if ((font = this._fontFamilies[this._font.name]) && isEqualFont(this._font, font)) {
      this._font = font;
      return this;
    }

    // save the font for reuse later
    if (cacheKey) {
      this._fontFamilies[cacheKey] = this._font;
    }
    if (this._font.name) {
      this._fontFamilies[this._font.name] = this._font;
    }
    return this;
  },
  fontSize: function fontSize(_fontSize) {
    this._fontSize = _fontSize;
    return this;
  },
  currentLineHeight: function currentLineHeight(includeGap) {
    if (includeGap == null) {
      includeGap = false;
    }
    return this._font.lineHeight(this._fontSize, includeGap);
  },
  registerFont: function registerFont(name, src, family) {
    this._registeredFonts[name] = {
      src: src,
      family: family
    };
    return this;
  }
};
var SOFT_HYPHEN = "\xAD";
var HYPHEN = '-';
var LineWrapper = /*#__PURE__*/function (_EventEmitter) {
  function LineWrapper(document, options) {
    var _this9;
    _this9 = _EventEmitter.call(this) || this;
    _this9.document = document;
    _this9.horizontalScaling = options.horizontalScaling || 100;
    _this9.indent = (options.indent || 0) * _this9.horizontalScaling / 100;
    _this9.characterSpacing = (options.characterSpacing || 0) * _this9.horizontalScaling / 100;
    _this9.wordSpacing = (options.wordSpacing === 0) * _this9.horizontalScaling / 100;
    _this9.columns = options.columns || 1;
    _this9.columnGap = (options.columnGap != null ? options.columnGap : 18) * _this9.horizontalScaling / 100; // 1/4 inch
    _this9.lineWidth = (options.width * _this9.horizontalScaling / 100 - _this9.columnGap * (_this9.columns - 1)) / _this9.columns;
    _this9.spaceLeft = _this9.lineWidth;
    _this9.startX = _this9.document.x;
    _this9.startY = _this9.document.y;
    _this9.column = 1;
    _this9.ellipsis = options.ellipsis;
    _this9.continuedX = 0;
    _this9.features = options.features;

    // calculate the maximum Y position the text can appear at
    if (options.height != null) {
      _this9.height = options.height;
      _this9.maxY = _this9.startY + options.height;
    } else {
      _this9.maxY = _this9.document.page.maxY();
    }

    // handle paragraph indents
    _this9.on('firstLine', function (options) {
      // if this is the first line of the text segment, and
      // we're continuing where we left off, indent that much
      // otherwise use the user specified indent option
      var indent = _this9.continuedX || _this9.indent;
      _this9.document.x += indent;
      _this9.lineWidth -= indent;

      // if indentAllLines is set to true
      // we're not resetting the indentation for this paragraph after the first line
      if (options.indentAllLines) {
        return;
      }

      // otherwise we start the next line without indent
      return _this9.once('line', function () {
        _this9.document.x -= indent;
        _this9.lineWidth += indent;
        if (options.continued && !_this9.continuedX) {
          _this9.continuedX = _this9.indent;
        }
        if (!options.continued) {
          return _this9.continuedX = 0;
        }
      });
    });

    // handle left aligning last lines of paragraphs
    _this9.on('lastLine', function (options) {
      var align = options.align;
      if (align === 'justify') {
        options.align = 'left';
      }
      _this9.lastLine = true;
      return _this9.once('line', function () {
        _this9.document.y += options.paragraphGap || 0;
        options.align = align;
        return _this9.lastLine = false;
      });
    });
    return _this9;
  }
  _inheritsLoose(LineWrapper, _EventEmitter);
  var _proto16 = LineWrapper.prototype;
  _proto16.wordWidth = function wordWidth(word) {
    return this.document.widthOfString(word, this) + this.characterSpacing + this.wordSpacing;
  };
  _proto16.canFit = function canFit(word, w) {
    if (word[word.length - 1] != SOFT_HYPHEN) {
      return w <= this.spaceLeft;
    }
    return w + this.wordWidth(HYPHEN) <= this.spaceLeft;
  };
  _proto16.eachWord = function eachWord(text, fn) {
    // setup a unicode line breaker
    var bk;
    var breaker = new _linebreak.default(text);
    var last = null;
    var wordWidths = Object.create(null);
    while (bk = breaker.nextBreak()) {
      var shouldContinue;
      var word = text.slice((last != null ? last.position : undefined) || 0, bk.position);
      var w = wordWidths[word] != null ? wordWidths[word] : wordWidths[word] = this.wordWidth(word);

      // if the word is longer than the whole line, chop it up
      // TODO: break by grapheme clusters, not JS string characters
      if (w > this.lineWidth + this.continuedX) {
        // make some fake break objects
        var lbk = last;
        var fbk = {};
        while (word.length) {
          // fit as much of the word as possible into the space we have
          var l, mightGrow;
          if (w > this.spaceLeft) {
            // start our check at the end of our available space - this method is faster than a loop of each character and it resolves
            // an issue with long loops when processing massive words, such as a huge number of spaces
            l = Math.ceil(this.spaceLeft / (w / word.length));
            w = this.wordWidth(word.slice(0, l));
            mightGrow = w <= this.spaceLeft && l < word.length;
          } else {
            l = word.length;
          }
          var mustShrink = w > this.spaceLeft && l > 0;
          // shrink or grow word as necessary after our near-guess above
          while (mustShrink || mightGrow) {
            if (mustShrink) {
              w = this.wordWidth(word.slice(0, --l));
              mustShrink = w > this.spaceLeft && l > 0;
            } else {
              w = this.wordWidth(word.slice(0, ++l));
              mustShrink = w > this.spaceLeft && l > 0;
              mightGrow = w <= this.spaceLeft && l < word.length;
            }
          }

          // check for the edge case where a single character cannot fit into a line.
          if (l === 0 && this.spaceLeft === this.lineWidth) {
            l = 1;
          }

          // send a required break unless this is the last piece and a linebreak is not specified
          fbk.required = bk.required || l < word.length;
          shouldContinue = fn(word.slice(0, l), w, fbk, lbk);
          lbk = {
            required: false
          };

          // get the remaining piece of the word
          word = word.slice(l);
          w = this.wordWidth(word);
          if (shouldContinue === false) {
            break;
          }
        }
      } else {
        // otherwise just emit the break as it was given to us
        shouldContinue = fn(word, w, bk, last);
      }
      if (shouldContinue === false) {
        break;
      }
      last = bk;
    }
  };
  _proto16.wrap = function wrap(text, options) {
    var _this10 = this;
    // override options from previous continued fragments
    this.horizontalScaling = options.horizontalScaling || 100;
    if (options.indent != null) {
      this.indent = options.indent * this.horizontalScaling / 100;
    }
    if (options.characterSpacing != null) {
      this.characterSpacing = options.characterSpacing * this.horizontalScaling / 100;
    }
    if (options.wordSpacing != null) {
      this.wordSpacing = options.wordSpacing * this.horizontalScaling / 100;
    }
    if (options.ellipsis != null) {
      this.ellipsis = options.ellipsis;
    }

    // make sure we're actually on the page
    // and that the first line of is never by
    // itself at the bottom of a page (orphans)
    var nextY = this.document.y + this.document.currentLineHeight(true);
    if (this.document.y > this.maxY || nextY > this.maxY) {
      this.nextSection();
    }
    var buffer = '';
    var textWidth = 0;
    var wc = 0;
    var lc = 0;
    var y = this.document.y; // used to reset Y pos if options.continued (below)
    var emitLine = function emitLine() {
      options.textWidth = textWidth + _this10.wordSpacing * (wc - 1);
      options.wordCount = wc;
      options.lineWidth = _this10.lineWidth;
      y = _this10.document.y;
      _this10.emit('line', buffer, options, _this10);
      return lc++;
    };
    this.emit('sectionStart', options, this);
    this.eachWord(text, function (word, w, bk, last) {
      if (last == null || last.required) {
        _this10.emit('firstLine', options, _this10);
        _this10.spaceLeft = _this10.lineWidth;
      }
      if (_this10.canFit(word, w)) {
        buffer += word;
        textWidth += w;
        wc++;
      }
      if (bk.required || !_this10.canFit(word, w)) {
        // if the user specified a max height and an ellipsis, and is about to pass the
        // max height and max columns after the next line, append the ellipsis
        var lh = _this10.document.currentLineHeight(true);
        if (_this10.height != null && _this10.ellipsis && _this10.document.y + lh * 2 > _this10.maxY && _this10.column >= _this10.columns) {
          if (_this10.ellipsis === true) {
            _this10.ellipsis = '…';
          } // map default ellipsis character
          buffer = buffer.replace(/\s+$/, '');
          textWidth = _this10.wordWidth(buffer + _this10.ellipsis);

          // remove characters from the buffer until the ellipsis fits
          // to avoid infinite loop need to stop while-loop if buffer is empty string
          while (buffer && textWidth > _this10.lineWidth) {
            buffer = buffer.slice(0, -1).replace(/\s+$/, '');
            textWidth = _this10.wordWidth(buffer + _this10.ellipsis);
          }
          // need to add ellipsis only if there is enough space for it
          if (textWidth <= _this10.lineWidth) {
            buffer = buffer + _this10.ellipsis;
          }
          textWidth = _this10.wordWidth(buffer);
        }
        if (bk.required) {
          if (w > _this10.spaceLeft) {
            emitLine();
            buffer = word;
            textWidth = w;
            wc = 1;
          }
          _this10.emit('lastLine', options, _this10);
        }

        // Previous entry is a soft hyphen - add visible hyphen.
        if (buffer[buffer.length - 1] == SOFT_HYPHEN) {
          buffer = buffer.slice(0, -1) + HYPHEN;
          _this10.spaceLeft -= _this10.wordWidth(HYPHEN);
        }
        emitLine();

        // if we've reached the edge of the page,
        // continue on a new page or column
        if (_this10.document.y + lh > _this10.maxY) {
          var shouldContinue = _this10.nextSection();

          // stop if we reached the maximum height
          if (!shouldContinue) {
            wc = 0;
            buffer = '';
            return false;
          }
        }

        // reset the space left and buffer
        if (bk.required) {
          _this10.spaceLeft = _this10.lineWidth;
          buffer = '';
          textWidth = 0;
          return wc = 0;
        } else {
          // reset the space left and buffer
          _this10.spaceLeft = _this10.lineWidth - w;
          buffer = word;
          textWidth = w;
          return wc = 1;
        }
      } else {
        return _this10.spaceLeft -= w;
      }
    });
    if (wc > 0) {
      this.emit('lastLine', options, this);
      emitLine();
    }
    this.emit('sectionEnd', options, this);

    // if the wrap is set to be continued, save the X position
    // to start the first line of the next segment at, and reset
    // the y position
    if (options.continued === true) {
      if (lc > 1) {
        this.continuedX = 0;
      }
      this.continuedX += options.textWidth || 0;
      return this.document.y = y;
    } else {
      return this.document.x = this.startX;
    }
  };
  _proto16.nextSection = function nextSection(options) {
    this.emit('sectionEnd', options, this);
    if (++this.column > this.columns) {
      // if a max height was specified by the user, we're done.
      // otherwise, the default is to make a new page at the bottom.
      if (this.height != null) {
        return false;
      }
      this.document.continueOnNewPage();
      this.column = 1;
      this.startY = this.document.page.margins.top;
      this.maxY = this.document.page.maxY();
      this.document.x = this.startX;
      if (this.document._fillColor) {
        var _this$document;
        (_this$document = this.document).fillColor.apply(_this$document, this.document._fillColor);
      }
      this.emit('pageBreak', options, this);
    } else {
      this.document.x += this.lineWidth + this.columnGap;
      this.document.y = this.startY;
      this.emit('columnBreak', options, this);
    }
    this.emit('sectionStart', options, this);
    return true;
  };
  return LineWrapper;
}(_events.EventEmitter);
var number$2 = PDFObject.number;
var TextMixin = {
  initText: function initText() {
    this._line = this._line.bind(this);
    // Current coordinates
    this.x = 0;
    this.y = 0;
    return this._lineGap = 0;
  },
  lineGap: function lineGap(_lineGap) {
    this._lineGap = _lineGap;
    return this;
  },
  moveDown: function moveDown(lines) {
    if (lines == null) {
      lines = 1;
    }
    this.y += this.currentLineHeight(true) * lines + this._lineGap;
    return this;
  },
  moveUp: function moveUp(lines) {
    if (lines == null) {
      lines = 1;
    }
    this.y -= this.currentLineHeight(true) * lines + this._lineGap;
    return this;
  },
  _text: function _text(text, x, y, options, lineCallback) {
    var _this11 = this;
    options = this._initOptions(x, y, options);

    // Convert text to a string
    text = text == null ? '' : "" + text;

    // if the wordSpacing option is specified, remove multiple consecutive spaces
    if (options.wordSpacing) {
      text = text.replace(/\s{2,}/g, ' ');
    }
    var addStructure = function addStructure() {
      if (options.structParent) {
        options.structParent.add(_this11.struct(options.structType || 'P', [_this11.markStructureContent(options.structType || 'P')]));
      }
    };

    // word wrapping
    if (options.width) {
      var wrapper = this._wrapper;
      if (!wrapper) {
        wrapper = new LineWrapper(this, options);
        wrapper.on('line', lineCallback);
        wrapper.on('firstLine', addStructure);
      }
      this._wrapper = options.continued ? wrapper : null;
      this._textOptions = options.continued ? options : null;
      wrapper.wrap(text, options);

      // render paragraphs as single lines
    } else {
      for (var _iterator9 = _createForOfIteratorHelperLoose(text.split('\n')), _step9; !(_step9 = _iterator9()).done;) {
        var line = _step9.value;
        addStructure();
        lineCallback(line, options);
      }
    }
    return this;
  },
  text: function text(_text2, x, y, options) {
    return this._text(_text2, x, y, options, this._line);
  },
  widthOfString: function widthOfString(string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var horizontalScaling = options.horizontalScaling || 100;
    return (this._font.widthOfString(string, this._fontSize, options.features) + (options.characterSpacing || 0) * (string.length - 1)) * horizontalScaling / 100;
  },
  heightOfString: function heightOfString(text, options) {
    var _this12 = this;
    var x = this.x,
      y = this.y;
    options = this._initOptions(options);
    options.height = Infinity; // don't break pages

    var lineGap = options.lineGap || this._lineGap || 0;
    this._text(text, this.x, this.y, options, function () {
      return _this12.y += _this12.currentLineHeight(true) + lineGap;
    });
    var height = this.y - y;
    this.x = x;
    this.y = y;
    return height;
  },
  list: function list(_list, x, y, options, wrapper) {
    options = this._initOptions(x, y, options);
    var listType = options.listType || 'bullet';
    var unit = Math.round(this._font.ascender / 1000 * this._fontSize);
    var midLine = unit / 2;
    var r = options.bulletRadius || unit / 3;
    var indent = options.textIndent || (listType === 'bullet' ? r * 5 : unit * 2);
    var itemIndent = options.bulletIndent || (listType === 'bullet' ? r * 8 : unit * 2);
    var level = 1;
    var items = [];
    var levels = [];
    var numbers = [];
    var _flatten = function flatten(list) {
      var n = 1;
      for (var i = 0; i < list.length; i++) {
        var item = list[i];
        if (Array.isArray(item)) {
          level++;
          _flatten(item);
          level--;
        } else {
          items.push(item);
          levels.push(level);
          if (listType !== 'bullet') {
            numbers.push(n++);
          }
        }
      }
    };
    _flatten(_list);
    var label = function label(n) {
      switch (listType) {
        case 'numbered':
          return n + ".";
        case 'lettered':
          var letter = String.fromCharCode((n - 1) % 26 + 65);
          var times = Math.floor((n - 1) / 26 + 1);
          var text = Array(times + 1).join(letter);
          return text + ".";
      }
    };
    var drawListItem = function drawListItem(listItem, i) {
      var _this13 = this;
      wrapper = new LineWrapper(this, options);
      wrapper.on('line', this._line);
      level = 1;
      wrapper.once('firstLine', function () {
        var item, itemType, labelType, bodyType;
        if (options.structParent) {
          if (options.structTypes) {
            var _options$structTypes = options.structTypes;
            itemType = _options$structTypes[0];
            labelType = _options$structTypes[1];
            bodyType = _options$structTypes[2];
          } else {
            itemType = 'LI';
            labelType = 'Lbl';
            bodyType = 'LBody';
          }
        }
        if (itemType) {
          item = _this13.struct(itemType);
          options.structParent.add(item);
        } else if (options.structParent) {
          item = options.structParent;
        }
        var l;
        if ((l = levels[i++]) !== level) {
          var diff = itemIndent * (l - level);
          _this13.x += diff;
          wrapper.lineWidth -= diff;
          level = l;
        }
        if (item && (labelType || bodyType)) {
          item.add(_this13.struct(labelType || bodyType, [_this13.markStructureContent(labelType || bodyType)]));
        }
        switch (listType) {
          case 'bullet':
            _this13.circle(_this13.x - indent + r, _this13.y + midLine, r);
            _this13.fill();
            break;
          case 'numbered':
          case 'lettered':
            var text = label(numbers[i - 1]);
            _this13._fragment(text, _this13.x - indent, _this13.y, options);
            break;
        }
        if (item && labelType && bodyType) {
          item.add(_this13.struct(bodyType, [_this13.markStructureContent(bodyType)]));
        }
        if (item && item !== options.structParent) {
          item.end();
        }
      });
      wrapper.on('sectionStart', function () {
        var pos = indent + itemIndent * (level - 1);
        _this13.x += pos;
        return wrapper.lineWidth -= pos;
      });
      wrapper.on('sectionEnd', function () {
        var pos = indent + itemIndent * (level - 1);
        _this13.x -= pos;
        return wrapper.lineWidth += pos;
      });
      wrapper.wrap(listItem, options);
    };
    for (var i = 0; i < items.length; i++) {
      drawListItem.call(this, items[i], i);
    }
    return this;
  },
  _initOptions: function _initOptions() {
    var x = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var y = arguments.length > 1 ? arguments[1] : undefined;
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    if (typeof x === 'object') {
      options = x;
      x = null;
    }

    // clone options object
    var result = Object.assign({}, options);

    // extend options with previous values for continued text
    if (this._textOptions) {
      for (var key in this._textOptions) {
        var val = this._textOptions[key];
        if (key !== 'continued') {
          if (result[key] === undefined) {
            result[key] = val;
          }
        }
      }
    }

    // Update the current position
    if (x != null) {
      this.x = x;
    }
    if (y != null) {
      this.y = y;
    }

    // wrap to margins if no x or y position passed
    if (result.lineBreak !== false) {
      if (result.width == null) {
        result.width = this.page.width - this.x - this.page.margins.right;
      }
      result.width = Math.max(result.width, 0);
    }
    if (!result.columns) {
      result.columns = 0;
    }
    if (result.columnGap == null) {
      result.columnGap = 18;
    } // 1/4 inch

    return result;
  },
  _line: function _line(text) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var wrapper = arguments.length > 2 ? arguments[2] : undefined;
    this._fragment(text, this.x, this.y, options);
    var lineGap = options.lineGap || this._lineGap || 0;
    if (!wrapper) {
      return this.x += this.widthOfString(text, options);
    } else {
      return this.y += this.currentLineHeight(true) + lineGap;
    }
  },
  _fragment: function _fragment(text, x, y, options) {
    var _this14 = this;
    var dy, encoded, i, positions, textWidth, words;
    text = ("" + text).replace(/\n/g, '');
    if (text.length === 0) {
      return;
    }

    // handle options
    var align = options.align || 'left';
    var wordSpacing = options.wordSpacing || 0;
    var characterSpacing = options.characterSpacing || 0;
    var horizontalScaling = options.horizontalScaling || 100;

    // text alignments
    if (options.width) {
      switch (align) {
        case 'right':
          textWidth = this.widthOfString(text.replace(/\s+$/, ''), options);
          x += options.lineWidth - textWidth;
          break;
        case 'center':
          x += options.lineWidth / 2 - options.textWidth / 2;
          break;
        case 'justify':
          // calculate the word spacing value
          words = text.trim().split(/\s+/);
          textWidth = this.widthOfString(text.replace(/\s+/g, ''), options);
          var spaceWidth = this.widthOfString(' ') + characterSpacing;
          wordSpacing = Math.max(0, (options.lineWidth - textWidth) / Math.max(1, words.length - 1) - spaceWidth);
          break;
      }
    }

    // text baseline alignments based on http://wiki.apache.org/xmlgraphics-fop/LineLayout/AlignmentHandling
    if (typeof options.baseline === 'number') {
      dy = -options.baseline;
    } else {
      switch (options.baseline) {
        case 'svg-middle':
          dy = 0.5 * this._font.xHeight;
          break;
        case 'middle':
        case 'svg-central':
          dy = 0.5 * (this._font.descender + this._font.ascender);
          break;
        case 'bottom':
        case 'ideographic':
          dy = this._font.descender;
          break;
        case 'alphabetic':
          dy = 0;
          break;
        case 'mathematical':
          dy = 0.5 * this._font.ascender;
          break;
        case 'hanging':
          dy = 0.8 * this._font.ascender;
          break;
        case 'top':
          dy = this._font.ascender;
          break;
        default:
          dy = this._font.ascender;
      }
      dy = dy / 1000 * this._fontSize;
    }

    // calculate the actual rendered width of the string after word and character spacing
    var renderedWidth = options.textWidth + wordSpacing * (options.wordCount - 1) + characterSpacing * (text.length - 1);

    // create link annotations if the link option is given
    if (options.link != null) {
      this.link(x, y, renderedWidth, this.currentLineHeight(), options.link);
    }
    if (options.goTo != null) {
      this.goTo(x, y, renderedWidth, this.currentLineHeight(), options.goTo);
    }
    if (options.destination != null) {
      this.addNamedDestination(options.destination, 'XYZ', x, y, null);
    }

    // create underline
    if (options.underline) {
      this.save();
      if (!options.stroke) {
        this.strokeColor.apply(this, this._fillColor || []);
      }
      var lineWidth = this._fontSize < 10 ? 0.5 : Math.floor(this._fontSize / 10);
      this.lineWidth(lineWidth);
      var lineY = y + this.currentLineHeight() - lineWidth;
      this.moveTo(x, lineY);
      this.lineTo(x + renderedWidth, lineY);
      this.stroke();
      this.restore();
    }

    // create strikethrough line
    if (options.strike) {
      this.save();
      if (!options.stroke) {
        this.strokeColor.apply(this, this._fillColor || []);
      }
      var _lineWidth = this._fontSize < 10 ? 0.5 : Math.floor(this._fontSize / 10);
      this.lineWidth(_lineWidth);
      var _lineY = y + this.currentLineHeight() / 2;
      this.moveTo(x, _lineY);
      this.lineTo(x + renderedWidth, _lineY);
      this.stroke();
      this.restore();
    }
    this.save();

    // oblique (angle in degrees or boolean)
    if (options.oblique) {
      var skew;
      if (typeof options.oblique === 'number') {
        skew = -Math.tan(options.oblique * Math.PI / 180);
      } else {
        skew = -0.25;
      }
      this.transform(1, 0, 0, 1, x, y);
      this.transform(1, 0, skew, 1, -skew * dy, 0);
      this.transform(1, 0, 0, 1, -x, -y);
    }

    // flip coordinate system
    this.transform(1, 0, 0, -1, 0, this.page.height);
    y = this.page.height - y - dy;

    // add current font to page if necessary
    if (this.page.fonts[this._font.id] == null) {
      this.page.fonts[this._font.id] = this._font.ref();
    }

    // begin the text object
    this.addContent('BT');

    // text position
    this.addContent("1 0 0 1 " + number$2(x) + " " + number$2(y) + " Tm");

    // font and font size
    this.addContent("/" + this._font.id + " " + number$2(this._fontSize) + " Tf");

    // rendering mode
    var mode = options.fill && options.stroke ? 2 : options.stroke ? 1 : 0;
    if (mode) {
      this.addContent(mode + " Tr");
    }

    // Character spacing
    if (characterSpacing) {
      this.addContent(number$2(characterSpacing) + " Tc");
    }

    // Horizontal scaling
    if (horizontalScaling !== 100) {
      this.addContent(horizontalScaling + " Tz");
    }

    // Add the actual text
    // If we have a word spacing value, we need to encode each word separately
    // since the normal Tw operator only works on character code 32, which isn't
    // used for embedded fonts.
    if (wordSpacing) {
      words = text.trim().split(/\s+/);
      wordSpacing += this.widthOfString(' ') + characterSpacing;
      wordSpacing *= 1000 / this._fontSize;
      encoded = [];
      positions = [];
      for (var _iterator10 = _createForOfIteratorHelperLoose(words), _step10; !(_step10 = _iterator10()).done;) {
        var word = _step10.value;
        var _this$_font$encode = this._font.encode(word, options.features),
          encodedWord = _this$_font$encode[0],
          positionsWord = _this$_font$encode[1];
        encoded = encoded.concat(encodedWord);
        positions = positions.concat(positionsWord);

        // add the word spacing to the end of the word
        // clone object because of cache
        var space = {};
        var object = positions[positions.length - 1];
        for (var key in object) {
          var val = object[key];
          space[key] = val;
        }
        space.xAdvance += wordSpacing;
        positions[positions.length - 1] = space;
      }
    } else {
      var _this$_font$encode2 = this._font.encode(text, options.features);
      encoded = _this$_font$encode2[0];
      positions = _this$_font$encode2[1];
    }
    var scale = this._fontSize / 1000;
    var commands = [];
    var last = 0;
    var hadOffset = false;

    // Adds a segment of text to the TJ command buffer
    var addSegment = function addSegment(cur) {
      if (last < cur) {
        var hex = encoded.slice(last, cur).join('');
        var advance = positions[cur - 1].xAdvance - positions[cur - 1].advanceWidth;
        commands.push("<" + hex + "> " + number$2(-advance));
      }
      return last = cur;
    };

    // Flushes the current TJ commands to the output stream
    var flush = function flush(i) {
      addSegment(i);
      if (commands.length > 0) {
        _this14.addContent("[" + commands.join(' ') + "] TJ");
        return commands.length = 0;
      }
    };
    for (i = 0; i < positions.length; i++) {
      // If we have an x or y offset, we have to break out of the current TJ command
      // so we can move the text position.
      var pos = positions[i];
      if (pos.xOffset || pos.yOffset) {
        // Flush the current buffer
        flush(i);

        // Move the text position and flush just the current character
        this.addContent("1 0 0 1 " + number$2(x + pos.xOffset * scale) + " " + number$2(y + pos.yOffset * scale) + " Tm");
        flush(i + 1);
        hadOffset = true;
      } else {
        // If the last character had an offset, reset the text position
        if (hadOffset) {
          this.addContent("1 0 0 1 " + number$2(x) + " " + number$2(y) + " Tm");
          hadOffset = false;
        }

        // Group segments that don't have any advance adjustments
        if (pos.xAdvance - pos.advanceWidth !== 0) {
          addSegment(i + 1);
        }
      }
      x += pos.xAdvance * scale;
    }

    // Flush any remaining commands
    flush(i);

    // end the text object
    this.addContent('ET');

    // restore flipped coordinate system
    return this.restore();
  }
};
var MARKERS = [0xffc0, 0xffc1, 0xffc2, 0xffc3, 0xffc5, 0xffc6, 0xffc7, 0xffc8, 0xffc9, 0xffca, 0xffcb, 0xffcc, 0xffcd, 0xffce, 0xffcf];
var COLOR_SPACE_MAP = {
  1: 'DeviceGray',
  3: 'DeviceRGB',
  4: 'DeviceCMYK'
};
var JPEG = /*#__PURE__*/function () {
  function JPEG(data, label) {
    var marker;
    this.data = data;
    this.label = label;
    if (this.data.readUInt16BE(0) !== 0xffd8) {
      throw 'SOI not found in JPEG';
    }

    // Parse the EXIF orientation
    this.orientation = _jpegExif.default.fromBuffer(this.data).Orientation || 1;
    var pos = 2;
    while (pos < this.data.length) {
      marker = this.data.readUInt16BE(pos);
      pos += 2;
      if (MARKERS.includes(marker)) {
        break;
      }
      pos += this.data.readUInt16BE(pos);
    }
    if (!MARKERS.includes(marker)) {
      throw 'Invalid JPEG.';
    }
    pos += 2;
    this.bits = this.data[pos++];
    this.height = this.data.readUInt16BE(pos);
    pos += 2;
    this.width = this.data.readUInt16BE(pos);
    pos += 2;
    var channels = this.data[pos++];
    this.colorSpace = COLOR_SPACE_MAP[channels];
    this.obj = null;
  }
  var _proto17 = JPEG.prototype;
  _proto17.embed = function embed(document) {
    if (this.obj) {
      return;
    }
    this.obj = document.ref({
      Type: 'XObject',
      Subtype: 'Image',
      BitsPerComponent: this.bits,
      Width: this.width,
      Height: this.height,
      ColorSpace: this.colorSpace,
      Filter: 'DCTDecode'
    });

    // add extra decode params for CMYK images. By swapping the
    // min and max values from the default, we invert the colors. See
    // section 4.8.4 of the spec.
    if (this.colorSpace === 'DeviceCMYK') {
      this.obj.data['Decode'] = [1.0, 0.0, 1.0, 0.0, 1.0, 0.0, 1.0, 0.0];
    }
    this.obj.end(this.data);

    // free memory
    return this.data = null;
  };
  return JPEG;
}();
var PNGImage = /*#__PURE__*/function () {
  function PNGImage(data, label) {
    this.label = label;
    this.image = new _pngJs.default(data);
    this.width = this.image.width;
    this.height = this.image.height;
    this.imgData = this.image.imgData;
    this.obj = null;
  }
  var _proto18 = PNGImage.prototype;
  _proto18.embed = function embed(document) {
    var dataDecoded = false;
    this.document = document;
    if (this.obj) {
      return;
    }
    var hasAlphaChannel = this.image.hasAlphaChannel;
    var isInterlaced = this.image.interlaceMethod === 1;
    this.obj = this.document.ref({
      Type: 'XObject',
      Subtype: 'Image',
      BitsPerComponent: hasAlphaChannel ? 8 : this.image.bits,
      Width: this.width,
      Height: this.height,
      Filter: 'FlateDecode'
    });
    if (!hasAlphaChannel) {
      var params = this.document.ref({
        Predictor: isInterlaced ? 1 : 15,
        Colors: this.image.colors,
        BitsPerComponent: this.image.bits,
        Columns: this.width
      });
      this.obj.data['DecodeParms'] = params;
      params.end();
    }
    if (this.image.palette.length === 0) {
      this.obj.data['ColorSpace'] = this.image.colorSpace;
    } else {
      // embed the color palette in the PDF as an object stream
      var palette = this.document.ref();
      palette.end(Buffer.from(this.image.palette));

      // build the color space array for the image
      this.obj.data['ColorSpace'] = ['Indexed', 'DeviceRGB', this.image.palette.length / 3 - 1, palette];
    }

    // For PNG color types 0, 2 and 3, the transparency data is stored in
    // a dedicated PNG chunk.
    if (this.image.transparency.grayscale != null) {
      // Use Color Key Masking (spec section 4.8.5)
      // An array with N elements, where N is two times the number of color components.
      var val = this.image.transparency.grayscale;
      this.obj.data['Mask'] = [val, val];
    } else if (this.image.transparency.rgb) {
      // Use Color Key Masking (spec section 4.8.5)
      // An array with N elements, where N is two times the number of color components.
      var rgb = this.image.transparency.rgb;
      var mask = [];
      for (var _iterator11 = _createForOfIteratorHelperLoose(rgb), _step11; !(_step11 = _iterator11()).done;) {
        var x = _step11.value;
        mask.push(x, x);
      }
      this.obj.data['Mask'] = mask;
    } else if (this.image.transparency.indexed) {
      // Create a transparency SMask for the image based on the data
      // in the PLTE and tRNS sections. See below for details on SMasks.
      dataDecoded = true;
      return this.loadIndexedAlphaChannel();
    } else if (hasAlphaChannel) {
      // For PNG color types 4 and 6, the transparency data is stored as a alpha
      // channel mixed in with the main image data. Separate this data out into an
      // SMask object and store it separately in the PDF.
      dataDecoded = true;
      return this.splitAlphaChannel();
    }
    if (isInterlaced && !dataDecoded) {
      return this.decodeData();
    }
    this.finalize();
  };
  _proto18.finalize = function finalize() {
    if (this.alphaChannel) {
      var sMask = this.document.ref({
        Type: 'XObject',
        Subtype: 'Image',
        Height: this.height,
        Width: this.width,
        BitsPerComponent: 8,
        Filter: 'FlateDecode',
        ColorSpace: 'DeviceGray',
        Decode: [0, 1]
      });
      sMask.end(this.alphaChannel);
      this.obj.data['SMask'] = sMask;
    }

    // add the actual image data
    this.obj.end(this.imgData);

    // free memory
    this.image = null;
    return this.imgData = null;
  };
  _proto18.splitAlphaChannel = function splitAlphaChannel() {
    var _this15 = this;
    return this.image.decodePixels(function (pixels) {
      var a, p;
      var colorCount = _this15.image.colors;
      var pixelCount = _this15.width * _this15.height;
      var imgData = Buffer.alloc(pixelCount * colorCount);
      var alphaChannel = Buffer.alloc(pixelCount);
      var i = p = a = 0;
      var len = pixels.length;
      // For 16bit images copy only most significant byte (MSB) - PNG data is always stored in network byte order (MSB first)
      var skipByteCount = _this15.image.bits === 16 ? 1 : 0;
      while (i < len) {
        for (var colorIndex = 0; colorIndex < colorCount; colorIndex++) {
          imgData[p++] = pixels[i++];
          i += skipByteCount;
        }
        alphaChannel[a++] = pixels[i++];
        i += skipByteCount;
      }
      _this15.imgData = _zlib.default.deflateSync(imgData);
      _this15.alphaChannel = _zlib.default.deflateSync(alphaChannel);
      return _this15.finalize();
    });
  };
  _proto18.loadIndexedAlphaChannel = function loadIndexedAlphaChannel() {
    var _this16 = this;
    var transparency = this.image.transparency.indexed;
    return this.image.decodePixels(function (pixels) {
      var alphaChannel = Buffer.alloc(_this16.width * _this16.height);
      var i = 0;
      for (var j = 0, end = pixels.length; j < end; j++) {
        alphaChannel[i++] = transparency[pixels[j]];
      }
      _this16.alphaChannel = _zlib.default.deflateSync(alphaChannel);
      return _this16.finalize();
    });
  };
  _proto18.decodeData = function decodeData() {
    var _this17 = this;
    this.image.decodePixels(function (pixels) {
      _this17.imgData = _zlib.default.deflateSync(pixels);
      _this17.finalize();
    });
  };
  return PNGImage;
}();
/*
PDFImage - embeds images in PDF documents
By Devon Govett
*/
var PDFImage = /*#__PURE__*/function () {
  function PDFImage() {}
  PDFImage.open = function open(src, label) {
    var data;
    if (Buffer.isBuffer(src)) {
      data = src;
    } else if (src instanceof ArrayBuffer) {
      data = Buffer.from(new Uint8Array(src));
    } else {
      var match;
      if (match = /^data:.+?;base64,(.*)$/.exec(src)) {
        data = Buffer.from(match[1], 'base64');
      } else {
        data = fs.readFileSync(src);
        if (!data) {
          return;
        }
      }
    }
    if (data[0] === 0xff && data[1] === 0xd8) {
      return new JPEG(data, label);
    } else if (data[0] === 0x89 && data.toString('ascii', 1, 4) === 'PNG') {
      return new PNGImage(data, label);
    } else {
      throw new Error('Unknown image format.');
    }
  };
  return PDFImage;
}();
var ImagesMixin = {
  initImages: function initImages() {
    this._imageRegistry = {};
    return this._imageCount = 0;
  },
  image: function image(src, x, y) {
    var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
    var bh, bp, bw, image, ip, left, left1, rotateAngle, originX, originY;
    if (typeof x === 'object') {
      options = x;
      x = null;
    }

    // Ignore orientation based on document options or image options
    var ignoreOrientation = options.ignoreOrientation || options.ignoreOrientation !== false && this.options.ignoreOrientation;
    x = (left = x != null ? x : options.x) != null ? left : this.x;
    y = (left1 = y != null ? y : options.y) != null ? left1 : this.y;
    if (typeof src === 'string') {
      image = this._imageRegistry[src];
    }
    if (!image) {
      if (src.width && src.height) {
        image = src;
      } else {
        image = this.openImage(src);
      }
    }
    if (!image.obj) {
      image.embed(this);
    }
    if (this.page.xobjects[image.label] == null) {
      this.page.xobjects[image.label] = image.obj;
    }
    var _image = image,
      width = _image.width,
      height = _image.height;

    // If EXIF orientation calls for it, swap width and height
    if (!ignoreOrientation && image.orientation > 4) {
      var _ref3 = [height, width];
      width = _ref3[0];
      height = _ref3[1];
    }
    var w = options.width || width;
    var h = options.height || height;
    if (options.width && !options.height) {
      var wp = w / width;
      w = width * wp;
      h = height * wp;
    } else if (options.height && !options.width) {
      var hp = h / height;
      w = width * hp;
      h = height * hp;
    } else if (options.scale) {
      w = width * options.scale;
      h = height * options.scale;
    } else if (options.fit) {
      var _options$fit = options.fit;
      bw = _options$fit[0];
      bh = _options$fit[1];
      bp = bw / bh;
      ip = width / height;
      if (ip > bp) {
        w = bw;
        h = bw / ip;
      } else {
        h = bh;
        w = bh * ip;
      }
    } else if (options.cover) {
      var _options$cover = options.cover;
      bw = _options$cover[0];
      bh = _options$cover[1];
      bp = bw / bh;
      ip = width / height;
      if (ip > bp) {
        h = bh;
        w = bh * ip;
      } else {
        w = bw;
        h = bw / ip;
      }
    }
    if (options.fit || options.cover) {
      if (options.align === 'center') {
        x = x + bw / 2 - w / 2;
      } else if (options.align === 'right') {
        x = x + bw - w;
      }
      if (options.valign === 'center') {
        y = y + bh / 2 - h / 2;
      } else if (options.valign === 'bottom') {
        y = y + bh - h;
      }
    }
    if (!ignoreOrientation) {
      switch (image.orientation) {
        // No orientation (need to flip image, though, because of the default transform matrix on the document)
        default:
        case 1:
          h = -h;
          y -= h;
          rotateAngle = 0;
          break;
        // Flip Horizontal
        case 2:
          w = -w;
          h = -h;
          x -= w;
          y -= h;
          rotateAngle = 0;
          break;
        // Rotate 180 degrees
        case 3:
          originX = x;
          originY = y;
          h = -h;
          x -= w;
          rotateAngle = 180;
          break;
        // Flip vertical
        case 4:
          // Do nothing, image will be flipped

          break;
        // Flip horizontally and rotate 270 degrees CW
        case 5:
          originX = x;
          originY = y;
          var _ref4 = [h, w];
          w = _ref4[0];
          h = _ref4[1];
          y -= h;
          rotateAngle = 90;
          break;
        // Rotate 90 degrees CW
        case 6:
          originX = x;
          originY = y;
          var _ref5 = [h, w];
          w = _ref5[0];
          h = _ref5[1];
          h = -h;
          rotateAngle = 90;
          break;
        // Flip horizontally and rotate 90 degrees CW
        case 7:
          originX = x;
          originY = y;
          var _ref6 = [h, w];
          w = _ref6[0];
          h = _ref6[1];
          h = -h;
          w = -w;
          x -= w;
          rotateAngle = 90;
          break;
        // Rotate 270 degrees CW
        case 8:
          originX = x;
          originY = y;
          var _ref7 = [h, w];
          w = _ref7[0];
          h = _ref7[1];
          h = -h;
          x -= w;
          y -= h;
          rotateAngle = -90;
          break;
      }
    } else {
      h = -h;
      y -= h;
      rotateAngle = 0;
    }

    // create link annotations if the link option is given
    if (options.link != null) {
      this.link(x, y, w, h, options.link);
    }
    if (options.goTo != null) {
      this.goTo(x, y, w, h, options.goTo);
    }
    if (options.destination != null) {
      this.addNamedDestination(options.destination, 'XYZ', x, y, null);
    }

    // Set the current y position to below the image if it is in the document flow
    if (this.y === y) {
      this.y += h;
    }
    this.save();
    if (rotateAngle) {
      this.rotate(rotateAngle, {
        origin: [originX, originY]
      });
    }
    this.transform(w, 0, 0, h, x, y);
    this.addContent("/" + image.label + " Do");
    this.restore();
    return this;
  },
  openImage: function openImage(src) {
    var image;
    if (typeof src === 'string') {
      image = this._imageRegistry[src];
    }
    if (!image) {
      image = PDFImage.open(src, "I" + ++this._imageCount);
      if (typeof src === 'string') {
        this._imageRegistry[src] = image;
      }
    }
    return image;
  }
};
var AnnotationsMixin = {
  annotate: function annotate(x, y, w, h, options) {
    options.Type = 'Annot';
    options.Rect = this._convertRect(x, y, w, h);
    options.Border = [0, 0, 0];
    if (options.Subtype === 'Link' && typeof options.F === 'undefined') {
      options.F = 1 << 2; // Print Annotation Flag
    }
    if (options.Subtype !== 'Link') {
      if (options.C == null) {
        options.C = this._normalizeColor(options.color || [0, 0, 0]);
      }
    } // convert colors
    delete options.color;
    if (typeof options.Dest === 'string') {
      options.Dest = new String(options.Dest);
    }

    // Capitalize keys
    for (var key in options) {
      var val = options[key];
      options[key[0].toUpperCase() + key.slice(1)] = val;
    }
    var ref = this.ref(options);
    this.page.annotations.push(ref);
    ref.end();
    return this;
  },
  note: function note(x, y, w, h, contents) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Text';
    options.Contents = new String(contents);
    if (options.Name == null) {
      options.Name = 'Comment';
    }
    if (options.color == null) {
      options.color = [243, 223, 92];
    }
    return this.annotate(x, y, w, h, options);
  },
  goTo: function goTo(x, y, w, h, name) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Link';
    options.A = this.ref({
      S: 'GoTo',
      D: new String(name)
    });
    options.A.end();
    return this.annotate(x, y, w, h, options);
  },
  link: function link(x, y, w, h, url) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'Link';
    if (typeof url === 'number') {
      // Link to a page in the document (the page must already exist)
      var pages = this._root.data.Pages.data;
      if (url >= 0 && url < pages.Kids.length) {
        options.A = this.ref({
          S: 'GoTo',
          D: [pages.Kids[url], 'XYZ', null, null, null]
        });
        options.A.end();
      } else {
        throw new Error("The document has no page " + url);
      }
    } else {
      // Link to an external url
      options.A = this.ref({
        S: 'URI',
        URI: new String(url)
      });
      options.A.end();
    }
    return this.annotate(x, y, w, h, options);
  },
  _markup: function _markup(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    var _this$_convertRect = this._convertRect(x, y, w, h),
      x1 = _this$_convertRect[0],
      y1 = _this$_convertRect[1],
      x2 = _this$_convertRect[2],
      y2 = _this$_convertRect[3];
    options.QuadPoints = [x1, y2, x2, y2, x1, y1, x2, y1];
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  highlight: function highlight(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Highlight';
    if (options.color == null) {
      options.color = [241, 238, 148];
    }
    return this._markup(x, y, w, h, options);
  },
  underline: function underline(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Underline';
    return this._markup(x, y, w, h, options);
  },
  strike: function strike(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'StrikeOut';
    return this._markup(x, y, w, h, options);
  },
  lineAnnotation: function lineAnnotation(x1, y1, x2, y2) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Line';
    options.Contents = new String();
    options.L = [x1, this.page.height - y1, x2, this.page.height - y2];
    return this.annotate(x1, y1, x2, y2, options);
  },
  rectAnnotation: function rectAnnotation(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Square';
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  ellipseAnnotation: function ellipseAnnotation(x, y, w, h) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    options.Subtype = 'Circle';
    options.Contents = new String();
    return this.annotate(x, y, w, h, options);
  },
  textAnnotation: function textAnnotation(x, y, w, h, text) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    options.Subtype = 'FreeText';
    options.Contents = new String(text);
    options.DA = new String();
    return this.annotate(x, y, w, h, options);
  },
  fileAnnotation: function fileAnnotation(x, y, w, h) {
    var file = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    // create hidden file
    var filespec = this.file(file.src, Object.assign({
      hidden: true
    }, file));
    options.Subtype = 'FileAttachment';
    options.FS = filespec;

    // add description from filespec unless description (Contents) has already been set
    if (options.Contents) {
      options.Contents = new String(options.Contents);
    } else if (filespec.data.Desc) {
      options.Contents = filespec.data.Desc;
    }
    return this.annotate(x, y, w, h, options);
  },
  _convertRect: function _convertRect(x1, y1, w, h) {
    // flip y1 and y2
    var y2 = y1;
    y1 += h;

    // make x2
    var x2 = x1 + w;

    // apply current transformation matrix to points
    var _this$_ctm = this._ctm,
      m0 = _this$_ctm[0],
      m1 = _this$_ctm[1],
      m2 = _this$_ctm[2],
      m3 = _this$_ctm[3],
      m4 = _this$_ctm[4],
      m5 = _this$_ctm[5];
    x1 = m0 * x1 + m2 * y1 + m4;
    y1 = m1 * x1 + m3 * y1 + m5;
    x2 = m0 * x2 + m2 * y2 + m4;
    y2 = m1 * x2 + m3 * y2 + m5;
    return [x1, y1, x2, y2];
  }
};
var PDFOutline = /*#__PURE__*/function () {
  function PDFOutline(document, parent, title, dest) {
    var options = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {
      expanded: false
    };
    this.document = document;
    this.options = options;
    this.outlineData = {};
    if (dest !== null) {
      this.outlineData['Dest'] = [dest.dictionary, 'Fit'];
    }
    if (parent !== null) {
      this.outlineData['Parent'] = parent;
    }
    if (title !== null) {
      this.outlineData['Title'] = new String(title);
    }
    this.dictionary = this.document.ref(this.outlineData);
    this.children = [];
  }
  var _proto19 = PDFOutline.prototype;
  _proto19.addItem = function addItem(title) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
      expanded: false
    };
    var result = new PDFOutline(this.document, this.dictionary, title, this.document.page, options);
    this.children.push(result);
    return result;
  };
  _proto19.endOutline = function endOutline() {
    if (this.children.length > 0) {
      if (this.options.expanded) {
        this.outlineData.Count = this.children.length;
      }
      var _first2 = this.children[0],
        _last2 = this.children[this.children.length - 1];
      this.outlineData.First = _first2.dictionary;
      this.outlineData.Last = _last2.dictionary;
      for (var i = 0, len = this.children.length; i < len; i++) {
        var child = this.children[i];
        if (i > 0) {
          child.outlineData.Prev = this.children[i - 1].dictionary;
        }
        if (i < this.children.length - 1) {
          child.outlineData.Next = this.children[i + 1].dictionary;
        }
        child.endOutline();
      }
    }
    return this.dictionary.end();
  };
  return PDFOutline;
}();
var OutlineMixin = {
  initOutline: function initOutline() {
    return this.outline = new PDFOutline(this, null, null, null);
  },
  endOutline: function endOutline() {
    this.outline.endOutline();
    if (this.outline.children.length > 0) {
      this._root.data.Outlines = this.outline.dictionary;
      return this._root.data.PageMode = 'UseOutlines';
    }
  }
};

/*
PDFStructureContent - a reference to a marked structure content
By Ben Schmidt
*/
var PDFStructureContent = /*#__PURE__*/function () {
  function PDFStructureContent(pageRef, mcid) {
    this.refs = [{
      pageRef: pageRef,
      mcid: mcid
    }];
  }
  var _proto20 = PDFStructureContent.prototype;
  _proto20.push = function push(structContent) {
    var _this18 = this;
    structContent.refs.forEach(function (ref) {
      return _this18.refs.push(ref);
    });
  };
  return PDFStructureContent;
}();
/*
PDFStructureElement - represents an element in the PDF logical structure tree
By Ben Schmidt
*/
var PDFStructureElement = /*#__PURE__*/function () {
  function PDFStructureElement(document, type) {
    var _this19 = this;
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var children = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    this.document = document;
    this._attached = false;
    this._ended = false;
    this._flushed = false;
    this.dictionary = document.ref({
      // Type: "StructElem",
      S: type
    });
    var data = this.dictionary.data;
    if (Array.isArray(options) || this._isValidChild(options)) {
      children = options;
      options = {};
    }
    if (typeof options.title !== 'undefined') {
      data.T = new String(options.title);
    }
    if (typeof options.lang !== 'undefined') {
      data.Lang = new String(options.lang);
    }
    if (typeof options.alt !== 'undefined') {
      data.Alt = new String(options.alt);
    }
    if (typeof options.expanded !== 'undefined') {
      data.E = new String(options.expanded);
    }
    if (typeof options.actual !== 'undefined') {
      data.ActualText = new String(options.actual);
    }
    this._children = [];
    if (children) {
      if (!Array.isArray(children)) {
        children = [children];
      }
      children.forEach(function (child) {
        return _this19.add(child);
      });
      this.end();
    }
  }
  var _proto21 = PDFStructureElement.prototype;
  _proto21.add = function add(child) {
    if (this._ended) {
      throw new Error("Cannot add child to already-ended structure element");
    }
    if (!this._isValidChild(child)) {
      throw new Error("Invalid structure element child");
    }
    if (child instanceof PDFStructureElement) {
      child.setParent(this.dictionary);
      if (this._attached) {
        child.setAttached();
      }
    }
    if (child instanceof PDFStructureContent) {
      this._addContentToParentTree(child);
    }
    if (typeof child === 'function' && this._attached) {
      // _contentForClosure() adds the content to the parent tree
      child = this._contentForClosure(child);
    }
    this._children.push(child);
    return this;
  };
  _proto21._addContentToParentTree = function _addContentToParentTree(content) {
    var _this20 = this;
    content.refs.forEach(function (_ref) {
      var pageRef = _ref.pageRef,
        mcid = _ref.mcid;
      var pageStructParents = _this20.document.getStructParentTree().get(pageRef.data.StructParents);
      pageStructParents[mcid] = _this20.dictionary;
    });
  };
  _proto21.setParent = function setParent(parentRef) {
    if (this.dictionary.data.P) {
      throw new Error("Structure element added to more than one parent");
    }
    this.dictionary.data.P = parentRef;
    this._flush();
  };
  _proto21.setAttached = function setAttached() {
    var _this21 = this;
    if (this._attached) {
      return;
    }
    this._children.forEach(function (child, index) {
      if (child instanceof PDFStructureElement) {
        child.setAttached();
      }
      if (typeof child === 'function') {
        _this21._children[index] = _this21._contentForClosure(child);
      }
    });
    this._attached = true;
    this._flush();
  };
  _proto21.end = function end() {
    if (this._ended) {
      return;
    }
    this._children.filter(function (child) {
      return child instanceof PDFStructureElement;
    }).forEach(function (child) {
      return child.end();
    });
    this._ended = true;
    this._flush();
  };
  _proto21._isValidChild = function _isValidChild(child) {
    return child instanceof PDFStructureElement || child instanceof PDFStructureContent || typeof child === 'function';
  };
  _proto21._contentForClosure = function _contentForClosure(closure) {
    var content = this.document.markStructureContent(this.dictionary.data.S);
    closure();
    this.document.endMarkedContent();
    this._addContentToParentTree(content);
    return content;
  };
  _proto21._isFlushable = function _isFlushable() {
    if (!this.dictionary.data.P || !this._ended) {
      return false;
    }
    return this._children.every(function (child) {
      if (typeof child === 'function') {
        return false;
      }
      if (child instanceof PDFStructureElement) {
        return child._isFlushable();
      }
      return true;
    });
  };
  _proto21._flush = function _flush() {
    var _this22 = this;
    if (this._flushed || !this._isFlushable()) {
      return;
    }
    this.dictionary.data.K = [];
    this._children.forEach(function (child) {
      return _this22._flushChild(child);
    });
    this.dictionary.end();

    // free memory used by children; the dictionary itself may still be
    // referenced by a parent structure element or root, but we can
    // at least trim the tree here
    this._children = [];
    this.dictionary.data.K = null;
    this._flushed = true;
  };
  _proto21._flushChild = function _flushChild(child) {
    var _this23 = this;
    if (child instanceof PDFStructureElement) {
      this.dictionary.data.K.push(child.dictionary);
    }
    if (child instanceof PDFStructureContent) {
      child.refs.forEach(function (_ref2) {
        var pageRef = _ref2.pageRef,
          mcid = _ref2.mcid;
        if (!_this23.dictionary.data.Pg) {
          _this23.dictionary.data.Pg = pageRef;
        }
        if (_this23.dictionary.data.Pg === pageRef) {
          _this23.dictionary.data.K.push(mcid);
        } else {
          _this23.dictionary.data.K.push({
            Type: "MCR",
            Pg: pageRef,
            MCID: mcid
          });
        }
      });
    }
  };
  return PDFStructureElement;
}();
/*
PDFNumberTree - represents a number tree object
*/
var PDFNumberTree = /*#__PURE__*/function (_PDFTree2) {
  function PDFNumberTree() {
    return _PDFTree2.apply(this, arguments) || this;
  }
  _inheritsLoose(PDFNumberTree, _PDFTree2);
  var _proto22 = PDFNumberTree.prototype;
  _proto22._compareKeys = function _compareKeys(a, b) {
    return parseInt(a) - parseInt(b);
  };
  _proto22._keysName = function _keysName() {
    return "Nums";
  };
  _proto22._dataForKey = function _dataForKey(k) {
    return parseInt(k);
  };
  return PDFNumberTree;
}(PDFTree);
/*
Markings mixin - support marked content sequences in content streams
By Ben Schmidt
*/
var MarkingsMixin = {
  initMarkings: function initMarkings(options) {
    this.structChildren = [];
    if (options.tagged) {
      this.getMarkInfoDictionary().data.Marked = true;
      this.getStructTreeRoot();
    }
  },
  markContent: function markContent(tag) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    if (tag === 'Artifact' || options && options.mcid) {
      var toClose = 0;
      this.page.markings.forEach(function (marking) {
        if (toClose || marking.structContent || marking.tag === 'Artifact') {
          toClose++;
        }
      });
      while (toClose--) {
        this.endMarkedContent();
      }
    }
    if (!options) {
      this.page.markings.push({
        tag: tag
      });
      this.addContent("/" + tag + " BMC");
      return this;
    }
    this.page.markings.push({
      tag: tag,
      options: options
    });
    var dictionary = {};
    if (typeof options.mcid !== 'undefined') {
      dictionary.MCID = options.mcid;
    }
    if (tag === 'Artifact') {
      if (typeof options.type === 'string') {
        dictionary.Type = options.type;
      }
      if (Array.isArray(options.bbox)) {
        dictionary.BBox = [options.bbox[0], this.page.height - options.bbox[3], options.bbox[2], this.page.height - options.bbox[1]];
      }
      if (Array.isArray(options.attached) && options.attached.every(function (val) {
        return typeof val === 'string';
      })) {
        dictionary.Attached = options.attached;
      }
    }
    if (tag === 'Span') {
      if (options.lang) {
        dictionary.Lang = new String(options.lang);
      }
      if (options.alt) {
        dictionary.Alt = new String(options.alt);
      }
      if (options.expanded) {
        dictionary.E = new String(options.expanded);
      }
      if (options.actual) {
        dictionary.ActualText = new String(options.actual);
      }
    }
    this.addContent("/" + tag + " " + PDFObject.convert(dictionary) + " BDC");
    return this;
  },
  markStructureContent: function markStructureContent(tag) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var pageStructParents = this.getStructParentTree().get(this.page.structParentTreeKey);
    var mcid = pageStructParents.length;
    pageStructParents.push(null);
    this.markContent(tag, Object.assign({}, options, {
      mcid: mcid
    }));
    var structContent = new PDFStructureContent(this.page.dictionary, mcid);
    this.page.markings.slice(-1)[0].structContent = structContent;
    return structContent;
  },
  endMarkedContent: function endMarkedContent() {
    this.page.markings.pop();
    this.addContent('EMC');
    return this;
  },
  struct: function struct(type) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var children = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    return new PDFStructureElement(this, type, options, children);
  },
  addStructure: function addStructure(structElem) {
    var structTreeRoot = this.getStructTreeRoot();
    structElem.setParent(structTreeRoot);
    structElem.setAttached();
    this.structChildren.push(structElem);
    if (!structTreeRoot.data.K) {
      structTreeRoot.data.K = [];
    }
    structTreeRoot.data.K.push(structElem.dictionary);
    return this;
  },
  initPageMarkings: function initPageMarkings(pageMarkings) {
    var _this24 = this;
    pageMarkings.forEach(function (marking) {
      if (marking.structContent) {
        var structContent = marking.structContent;
        var newStructContent = _this24.markStructureContent(marking.tag, marking.options);
        structContent.push(newStructContent);
        _this24.page.markings.slice(-1)[0].structContent = structContent;
      } else {
        _this24.markContent(marking.tag, marking.options);
      }
    });
  },
  endPageMarkings: function endPageMarkings(page) {
    var pageMarkings = page.markings;
    pageMarkings.forEach(function () {
      return page.write('EMC');
    });
    page.markings = [];
    return pageMarkings;
  },
  getMarkInfoDictionary: function getMarkInfoDictionary() {
    if (!this._root.data.MarkInfo) {
      this._root.data.MarkInfo = this.ref({});
    }
    return this._root.data.MarkInfo;
  },
  hasMarkInfoDictionary: function hasMarkInfoDictionary() {
    return !!this._root.data.MarkInfo;
  },
  getStructTreeRoot: function getStructTreeRoot() {
    if (!this._root.data.StructTreeRoot) {
      this._root.data.StructTreeRoot = this.ref({
        Type: 'StructTreeRoot',
        ParentTree: new PDFNumberTree(),
        ParentTreeNextKey: 0
      });
    }
    return this._root.data.StructTreeRoot;
  },
  getStructParentTree: function getStructParentTree() {
    return this.getStructTreeRoot().data.ParentTree;
  },
  createStructParentTreeNextKey: function createStructParentTreeNextKey() {
    // initialise the MarkInfo dictionary
    this.getMarkInfoDictionary();
    var structTreeRoot = this.getStructTreeRoot();
    var key = structTreeRoot.data.ParentTreeNextKey++;
    structTreeRoot.data.ParentTree.add(key, []);
    return key;
  },
  endMarkings: function endMarkings() {
    var structTreeRoot = this._root.data.StructTreeRoot;
    if (structTreeRoot) {
      structTreeRoot.end();
      this.structChildren.forEach(function (structElem) {
        return structElem.end();
      });
    }
    if (this._root.data.MarkInfo) {
      this._root.data.MarkInfo.end();
    }
  }
};
var FIELD_FLAGS = {
  readOnly: 1,
  required: 2,
  noExport: 4,
  multiline: 0x1000,
  password: 0x2000,
  toggleToOffButton: 0x4000,
  radioButton: 0x8000,
  pushButton: 0x10000,
  combo: 0x20000,
  edit: 0x40000,
  sort: 0x80000,
  multiSelect: 0x200000,
  noSpell: 0x400000
};
var FIELD_JUSTIFY = {
  left: 0,
  center: 1,
  right: 2
};
var VALUE_MAP = {
  value: 'V',
  defaultValue: 'DV'
};
var FORMAT_SPECIAL = {
  zip: '0',
  zipPlus4: '1',
  zip4: '1',
  phone: '2',
  ssn: '3'
};
var FORMAT_DEFAULT = {
  number: {
    nDec: 0,
    sepComma: false,
    negStyle: 'MinusBlack',
    currency: '',
    currencyPrepend: true
  },
  percent: {
    nDec: 0,
    sepComma: false
  }
};
var AcroFormMixin = {
  /**
   * Must call if adding AcroForms to a document. Must also call font() before
   * this method to set the default font.
   */
  initForm: function initForm() {
    if (!this._font) {
      throw new Error('Must set a font before calling initForm method');
    }
    this._acroform = {
      fonts: {},
      defaultFont: this._font.name
    };
    this._acroform.fonts[this._font.id] = this._font.ref();
    var data = {
      Fields: [],
      NeedAppearances: true,
      DA: new String("/" + this._font.id + " 0 Tf 0 g"),
      DR: {
        Font: {}
      }
    };
    data.DR.Font[this._font.id] = this._font.ref();
    var AcroForm = this.ref(data);
    this._root.data.AcroForm = AcroForm;
    return this;
  },
  /**
   * Called automatically by document.js
   */
  endAcroForm: function endAcroForm() {
    var _this25 = this;
    if (this._root.data.AcroForm) {
      if (!Object.keys(this._acroform.fonts).length && !this._acroform.defaultFont) {
        throw new Error('No fonts specified for PDF form');
      }
      var fontDict = this._root.data.AcroForm.data.DR.Font;
      Object.keys(this._acroform.fonts).forEach(function (name) {
        fontDict[name] = _this25._acroform.fonts[name];
      });
      this._root.data.AcroForm.data.Fields.forEach(function (fieldRef) {
        _this25._endChild(fieldRef);
      });
      this._root.data.AcroForm.end();
    }
    return this;
  },
  _endChild: function _endChild(ref) {
    var _this26 = this;
    if (Array.isArray(ref.data.Kids)) {
      ref.data.Kids.forEach(function (childRef) {
        _this26._endChild(childRef);
      });
      ref.end();
    }
    return this;
  },
  /**
   * Creates and adds a form field to the document. Form fields are intermediate
   * nodes in a PDF form that are used to specify form name heirarchy and form
   * value defaults.
   * @param {string} name - field name (T attribute in field dictionary)
   * @param {object} options  - other attributes to include in field dictionary
   */
  formField: function formField(name) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var fieldDict = this._fieldDict(name, null, options);
    var fieldRef = this.ref(fieldDict);
    this._addToParent(fieldRef);
    return fieldRef;
  },
  /**
   * Creates and adds a Form Annotation to the document. Form annotations are
   * called Widget annotations internally within a PDF file.
   * @param {string} name - form field name (T attribute of widget annotation
   * dictionary)
   * @param {number} x
   * @param {number} y
   * @param {number} w
   * @param {number} h
   * @param {object} options
   */
  formAnnotation: function formAnnotation(name, type, x, y, w, h) {
    var options = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
    var fieldDict = this._fieldDict(name, type, options);
    fieldDict.Subtype = 'Widget';
    if (fieldDict.F === undefined) {
      fieldDict.F = 4; // print the annotation
    }

    // Add Field annot to page, and get it's ref
    this.annotate(x, y, w, h, fieldDict);
    var annotRef = this.page.annotations[this.page.annotations.length - 1];
    return this._addToParent(annotRef);
  },
  formText: function formText(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'text', x, y, w, h, options);
  },
  formPushButton: function formPushButton(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'pushButton', x, y, w, h, options);
  },
  formCombo: function formCombo(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'combo', x, y, w, h, options);
  },
  formList: function formList(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'list', x, y, w, h, options);
  },
  formRadioButton: function formRadioButton(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'radioButton', x, y, w, h, options);
  },
  formCheckbox: function formCheckbox(name, x, y, w, h) {
    var options = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {};
    return this.formAnnotation(name, 'checkbox', x, y, w, h, options);
  },
  _addToParent: function _addToParent(fieldRef) {
    var parent = fieldRef.data.Parent;
    if (parent) {
      if (!parent.data.Kids) {
        parent.data.Kids = [];
      }
      parent.data.Kids.push(fieldRef);
    } else {
      this._root.data.AcroForm.data.Fields.push(fieldRef);
    }
    return this;
  },
  _fieldDict: function _fieldDict(name, type) {
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    if (!this._acroform) {
      throw new Error('Call document.initForm() method before adding form elements to document');
    }
    var opts = Object.assign({}, options);
    if (type !== null) {
      opts = this._resolveType(type, options);
    }
    opts = this._resolveFlags(opts);
    opts = this._resolveJustify(opts);
    opts = this._resolveFont(opts);
    opts = this._resolveStrings(opts);
    opts = this._resolveColors(opts);
    opts = this._resolveFormat(opts);
    opts.T = new String(name);
    if (opts.parent) {
      opts.Parent = opts.parent;
      delete opts.parent;
    }
    return opts;
  },
  _resolveType: function _resolveType(type, opts) {
    if (type === 'text') {
      opts.FT = 'Tx';
    } else if (type === 'pushButton') {
      opts.FT = 'Btn';
      opts.pushButton = true;
    } else if (type === 'radioButton') {
      opts.FT = 'Btn';
      opts.radioButton = true;
    } else if (type === 'checkbox') {
      opts.FT = 'Btn';
    } else if (type === 'combo') {
      opts.FT = 'Ch';
      opts.combo = true;
    } else if (type === 'list') {
      opts.FT = 'Ch';
    } else {
      throw new Error("Invalid form annotation type '" + type + "'");
    }
    return opts;
  },
  _resolveFormat: function _resolveFormat(opts) {
    var f = opts.format;
    if (f && f.type) {
      var fnKeystroke;
      var fnFormat;
      var params = '';
      if (FORMAT_SPECIAL[f.type] !== undefined) {
        fnKeystroke = "AFSpecial_Keystroke";
        fnFormat = "AFSpecial_Format";
        params = FORMAT_SPECIAL[f.type];
      } else {
        var format = f.type.charAt(0).toUpperCase() + f.type.slice(1);
        fnKeystroke = "AF" + format + "_Keystroke";
        fnFormat = "AF" + format + "_Format";
        if (f.type === 'date') {
          fnKeystroke += 'Ex';
          params = String(f.param);
        } else if (f.type === 'time') {
          params = String(f.param);
        } else if (f.type === 'number') {
          var p = Object.assign({}, FORMAT_DEFAULT.number, f);
          params = String([String(p.nDec), p.sepComma ? '0' : '1', '"' + p.negStyle + '"', 'null', '"' + p.currency + '"', String(p.currencyPrepend)].join(','));
        } else if (f.type === 'percent') {
          var _p = Object.assign({}, FORMAT_DEFAULT.percent, f);
          params = String([String(_p.nDec), _p.sepComma ? '0' : '1'].join(','));
        }
      }
      opts.AA = opts.AA ? opts.AA : {};
      opts.AA.K = {
        S: 'JavaScript',
        JS: new String(fnKeystroke + "(" + params + ");")
      };
      opts.AA.F = {
        S: 'JavaScript',
        JS: new String(fnFormat + "(" + params + ");")
      };
    }
    delete opts.format;
    return opts;
  },
  _resolveColors: function _resolveColors(opts) {
    var color = this._normalizeColor(opts.backgroundColor);
    if (color) {
      if (!opts.MK) {
        opts.MK = {};
      }
      opts.MK.BG = color;
    }
    color = this._normalizeColor(opts.borderColor);
    if (color) {
      if (!opts.MK) {
        opts.MK = {};
      }
      opts.MK.BC = color;
    }
    delete opts.backgroundColor;
    delete opts.borderColor;
    return opts;
  },
  _resolveFlags: function _resolveFlags(options) {
    var result = 0;
    Object.keys(options).forEach(function (key) {
      if (FIELD_FLAGS[key]) {
        if (options[key]) {
          result |= FIELD_FLAGS[key];
        }
        delete options[key];
      }
    });
    if (result !== 0) {
      options.Ff = options.Ff ? options.Ff : 0;
      options.Ff |= result;
    }
    return options;
  },
  _resolveJustify: function _resolveJustify(options) {
    var result = 0;
    if (options.align !== undefined) {
      if (typeof FIELD_JUSTIFY[options.align] === 'number') {
        result = FIELD_JUSTIFY[options.align];
      }
      delete options.align;
    }
    if (result !== 0) {
      options.Q = result; // default
    }
    return options;
  },
  _resolveFont: function _resolveFont(options) {
    // add current font to document-level AcroForm dict if necessary
    if (this._acroform.fonts[this._font.id] == null) {
      this._acroform.fonts[this._font.id] = this._font.ref();
    }

    // add current font to field's resource dict (RD) if not the default acroform font
    if (this._acroform.defaultFont !== this._font.name) {
      options.DR = {
        Font: {}
      };

      // Get the fontSize option. If not set use auto sizing
      var fontSize = options.fontSize || 0;
      options.DR.Font[this._font.id] = this._font.ref();
      options.DA = new String("/" + this._font.id + " " + fontSize + " Tf 0 g");
    }
    return options;
  },
  _resolveStrings: function _resolveStrings(options) {
    var select = [];
    function appendChoices(a) {
      if (Array.isArray(a)) {
        for (var idx = 0; idx < a.length; idx++) {
          if (typeof a[idx] === 'string') {
            select.push(new String(a[idx]));
          } else {
            select.push(a[idx]);
          }
        }
      }
    }
    appendChoices(options.Opt);
    if (options.select) {
      appendChoices(options.select);
      delete options.select;
    }
    if (select.length) {
      options.Opt = select;
    }
    Object.keys(VALUE_MAP).forEach(function (key) {
      if (options[key] !== undefined) {
        options[VALUE_MAP[key]] = options[key];
        delete options[key];
      }
    });
    ['V', 'DV'].forEach(function (key) {
      if (typeof options[key] === 'string') {
        options[key] = new String(options[key]);
      }
    });
    if (options.MK && options.MK.CA) {
      options.MK.CA = new String(options.MK.CA);
    }
    if (options.label) {
      options.MK = options.MK ? options.MK : {};
      options.MK.CA = new String(options.label);
      delete options.label;
    }
    return options;
  }
};
var AttachmentsMixin = {
  /**
   * Embed contents of `src` in PDF
   * @param {Buffer | ArrayBuffer | string} src input Buffer, ArrayBuffer, base64 encoded string or path to file
   * @param {object} options
   *  * options.name: filename to be shown in PDF, will use `src` if none set
   *  * options.type: filetype to be shown in PDF
   *  * options.description: description to be shown in PDF
   *  * options.hidden: if true, do not add attachment to EmbeddedFiles dictionary. Useful for file attachment annotations
   *  * options.creationDate: override creation date
   *  * options.modifiedDate: override modified date
   *  * options.relationship: Relationship between the PDF document and its attached file. Can be 'Alternative', 'Data', 'Source', 'Supplement' or 'Unspecified'.
   * @returns filespec reference
   */
  file: function file(src) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    options.name = options.name || src;
    options.relationship = options.relationship || 'Unspecified';
    var refBody = {
      Type: 'EmbeddedFile',
      Params: {}
    };
    var data;
    if (!src) {
      throw new Error('No src specified');
    }
    if (Buffer.isBuffer(src)) {
      data = src;
    } else if (src instanceof ArrayBuffer) {
      data = Buffer.from(new Uint8Array(src));
    } else {
      var match;
      if (match = /^data:(.*?);base64,(.*)$/.exec(src)) {
        if (match[1]) {
          refBody.Subtype = match[1].replace('/', '#2F');
        }
        data = Buffer.from(match[2], 'base64');
      } else {
        data = fs.readFileSync(src);
        if (!data) {
          throw new Error("Could not read contents of file at filepath " + src);
        }

        // update CreationDate and ModDate
        var _fs$statSync = fs.statSync(src),
          birthtime = _fs$statSync.birthtime,
          ctime = _fs$statSync.ctime;
        refBody.Params.CreationDate = birthtime;
        refBody.Params.ModDate = ctime;
      }
    }

    // override creation date and modified date
    if (options.creationDate instanceof Date) {
      refBody.Params.CreationDate = options.creationDate;
    }
    if (options.modifiedDate instanceof Date) {
      refBody.Params.ModDate = options.modifiedDate;
    }
    // add optional subtype
    if (options.type) {
      refBody.Subtype = options.type.replace('/', '#2F');
    }

    // add checksum and size information
    var checksum = _cryptoJs.default.MD5(_cryptoJs.default.lib.WordArray.create(new Uint8Array(data)));
    refBody.Params.CheckSum = new String(checksum);
    refBody.Params.Size = data.byteLength;

    // save some space when embedding the same file again
    // if a file with the same name and metadata exists, reuse its reference
    var ref;
    if (!this._fileRegistry) this._fileRegistry = {};
    var file = this._fileRegistry[options.name];
    if (file && isEqual(refBody, file)) {
      ref = file.ref;
    } else {
      ref = this.ref(refBody);
      ref.end(data);
      this._fileRegistry[options.name] = Object.assign({}, refBody, {
        ref: ref
      });
    }
    // add filespec for embedded file
    var fileSpecBody = {
      Type: 'Filespec',
      AFRelationship: options.relationship,
      F: new String(options.name),
      EF: {
        F: ref
      },
      UF: new String(options.name)
    };
    if (options.description) {
      fileSpecBody.Desc = new String(options.description);
    }
    var filespec = this.ref(fileSpecBody);
    filespec.end();
    if (!options.hidden) {
      this.addNamedEmbeddedFile(options.name, filespec);
    }

    // Add file to the catalogue to be PDF/A3 compliant
    if (this._root.data.AF) {
      this._root.data.AF.push(filespec);
    } else {
      this._root.data.AF = [filespec];
    }
    return filespec;
  }
};

/** check two embedded file metadata objects for equality */
function isEqual(a, b) {
  return a.Subtype === b.Subtype && a.Params.CheckSum.toString() === b.Params.CheckSum.toString() && a.Params.Size === b.Params.Size && a.Params.CreationDate.getTime() === b.Params.CreationDate.getTime() && (a.Params.ModDate === undefined && b.Params.ModDate === undefined || a.Params.ModDate.getTime() === b.Params.ModDate.getTime());
}
var PDFA = {
  initPDFA: function initPDFA(pSubset) {
    if (pSubset.charAt(pSubset.length - 3) === '-') {
      this.subset_conformance = pSubset.charAt(pSubset.length - 1).toUpperCase();
      this.subset = parseInt(pSubset.charAt(pSubset.length - 2));
    } else {
      // Default to Basic conformance when user doesn't specify
      this.subset_conformance = 'B';
      this.subset = parseInt(pSubset.charAt(pSubset.length - 1));
    }
  },
  endSubset: function endSubset() {
    this._addPdfaMetadata();
    this._addColorOutputIntent();
  },
  _addColorOutputIntent: function _addColorOutputIntent() {
    var iccProfile = Buffer("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", "base64");
    ;
    var colorProfileRef = this.ref({
      Length: iccProfile.length,
      N: 3
    });
    colorProfileRef.write(iccProfile);
    colorProfileRef.end();
    var intentRef = this.ref({
      Type: 'OutputIntent',
      S: 'GTS_PDFA1',
      Info: new String('sRGB IEC61966-2.1'),
      OutputConditionIdentifier: new String('sRGB IEC61966-2.1'),
      DestOutputProfile: colorProfileRef
    });
    intentRef.end();
    this._root.data.OutputIntents = [intentRef];
  },
  _getPdfaid: function _getPdfaid() {
    return "\n        <rdf:Description xmlns:pdfaid=\"http://www.aiim.org/pdfa/ns/id/\" rdf:about=\"\">\n            <pdfaid:part>" + this.subset + "</pdfaid:part>\n            <pdfaid:conformance>" + this.subset_conformance + "</pdfaid:conformance>\n        </rdf:Description>\n        ";
  },
  _addPdfaMetadata: function _addPdfaMetadata() {
    this.appendXML(this._getPdfaid());
  }
};
var PDFUA = {
  initPDFUA: function initPDFUA() {
    this.subset = 1;
  },
  endSubset: function endSubset() {
    this._addPdfuaMetadata();
  },
  _addPdfuaMetadata: function _addPdfuaMetadata() {
    this.appendXML(this._getPdfuaid());
  },
  _getPdfuaid: function _getPdfuaid() {
    return "\n        <rdf:Description xmlns:pdfuaid=\"http://www.aiim.org/pdfua/ns/id/\" rdf:about=\"\">\n            <pdfuaid:part>" + this.subset + "</pdfuaid:part>\n        </rdf:Description>\n        ";
  }
};
var SubsetMixin = {
  _importSubset: function _importSubset(subset) {
    Object.assign(this, subset);
  },
  initSubset: function initSubset(options) {
    switch (options.subset) {
      case 'PDF/A-1':
      case 'PDF/A-1a':
      case 'PDF/A-1b':
      case 'PDF/A-2':
      case 'PDF/A-2a':
      case 'PDF/A-2b':
      case 'PDF/A-3':
      case 'PDF/A-3a':
      case 'PDF/A-3b':
        this._importSubset(PDFA);
        this.initPDFA(options.subset);
        break;
      case 'PDF/UA':
        this._importSubset(PDFUA);
        this.initPDFUA();
        break;
    }
  }
};
var PDFMetadata = /*#__PURE__*/function () {
  function PDFMetadata() {
    this._metadata = "\n        <?xpacket begin=\"\uFEFF\" id=\"W5M0MpCehiHzreSzNTczkc9d\"?>\n            <x:xmpmeta xmlns:x=\"adobe:ns:meta/\">\n                <rdf:RDF xmlns:rdf=\"http://www.w3.org/1999/02/22-rdf-syntax-ns#\">\n        ";
  }
  var _proto23 = PDFMetadata.prototype;
  _proto23._closeTags = function _closeTags() {
    this._metadata = this._metadata.concat("\n                </rdf:RDF>\n            </x:xmpmeta>\n        <?xpacket end=\"w\"?>\n        ");
  };
  _proto23.append = function append(xml) {
    var newline = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    this._metadata = this._metadata.concat(xml);
    if (newline) this._metadata = this._metadata.concat('\n');
  };
  _proto23.getXML = function getXML() {
    return this._metadata;
  };
  _proto23.getLength = function getLength() {
    return this._metadata.length;
  };
  _proto23.end = function end() {
    this._closeTags();
    this._metadata = this._metadata.trim();
  };
  return PDFMetadata;
}();
var MetadataMixin = {
  initMetadata: function initMetadata() {
    this.metadata = new PDFMetadata();
  },
  appendXML: function appendXML(xml) {
    var newline = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    this.metadata.append(xml, newline);
  },
  _addInfo: function _addInfo() {
    this.appendXML("\n        <rdf:Description rdf:about=\"\" xmlns:xmp=\"http://ns.adobe.com/xap/1.0/\">\n            <xmp:CreateDate>" + (this.info.CreationDate.toISOString().split('.')[0] + "Z") + "</xmp:CreateDate>\n            <xmp:CreatorTool>" + this.info.Creator + "</xmp:CreatorTool>\n        </rdf:Description>\n        ");
    if (this.info.Title || this.info.Author || this.info.Subject) {
      this.appendXML("\n            <rdf:Description rdf:about=\"\" xmlns:dc=\"http://purl.org/dc/elements/1.1/\">\n            ");
      if (this.info.Title) {
        this.appendXML("\n                <dc:title>\n                    <rdf:Alt>\n                        <rdf:li xml:lang=\"x-default\">" + this.info.Title + "</rdf:li>\n                    </rdf:Alt>\n                </dc:title>\n                ");
      }
      if (this.info.Author) {
        this.appendXML("\n                <dc:creator>\n                    <rdf:Seq>\n                        <rdf:li>" + this.info.Author + "</rdf:li>\n                    </rdf:Seq>\n                </dc:creator>\n                ");
      }
      if (this.info.Subject) {
        this.appendXML("\n                <dc:description>\n                    <rdf:Alt>\n                        <rdf:li xml:lang=\"x-default\">" + this.info.Subject + "</rdf:li>\n                    </rdf:Alt>\n                </dc:description>\n                ");
      }
      this.appendXML("\n            </rdf:Description>\n            ");
    }
    this.appendXML("\n        <rdf:Description rdf:about=\"\" xmlns:pdf=\"http://ns.adobe.com/pdf/1.3/\">\n            <pdf:Producer>" + this.info.Creator + "</pdf:Producer>", false);
    if (this.info.Keywords) {
      this.appendXML("\n            <pdf:Keywords>" + this.info.Keywords + "</pdf:Keywords>", false);
    }
    this.appendXML("\n        </rdf:Description>\n        ");
  },
  endMetadata: function endMetadata() {
    this._addInfo();
    this.metadata.end();

    /*
    Metadata was introduced in PDF 1.4, so adding it to 1.3 
    will likely only take up more space.
    */
    if (this.version != 1.3) {
      this.metadataRef = this.ref({
        length: this.metadata.getLength(),
        Type: 'Metadata',
        Subtype: 'XML'
      });
      this.metadataRef.compress = false;
      this.metadataRef.write(Buffer.from(this.metadata.getXML(), 'utf-8'));
      this.metadataRef.end();
      this._root.data.Metadata = this.metadataRef;
    }
  }
};

/*
PDFDocument - represents an entire PDF document
By Devon Govett
*/
var PDFDocument = /*#__PURE__*/function (_stream$Readable) {
  function PDFDocument() {
    var _this27;
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _this27 = _stream$Readable.call(this, options) || this;
    _this27.options = options;

    // PDF version
    switch (options.pdfVersion) {
      case '1.4':
        _this27.version = 1.4;
        break;
      case '1.5':
        _this27.version = 1.5;
        break;
      case '1.6':
        _this27.version = 1.6;
        break;
      case '1.7':
      case '1.7ext3':
        _this27.version = 1.7;
        break;
      default:
        _this27.version = 1.3;
        break;
    }

    // Whether streams should be compressed
    _this27.compress = _this27.options.compress != null ? _this27.options.compress : true;
    _this27._pageBuffer = [];
    _this27._pageBufferStart = 0;

    // The PDF object store
    _this27._offsets = [];
    _this27._waiting = 0;
    _this27._ended = false;
    _this27._offset = 0;
    var Pages = _this27.ref({
      Type: 'Pages',
      Count: 0,
      Kids: []
    });
    var Names = _this27.ref({
      Dests: new PDFNameTree()
    });
    _this27._root = _this27.ref({
      Type: 'Catalog',
      Pages: Pages,
      Names: Names
    });
    if (_this27.options.lang) {
      _this27._root.data.Lang = new String(_this27.options.lang);
    }

    // The current page
    _this27.page = null;

    // Initialize mixins
    _this27.initMetadata();
    _this27.initColor();
    _this27.initVector();
    _this27.initFonts(options.font);
    _this27.initText();
    _this27.initImages();
    _this27.initOutline();
    _this27.initMarkings(options);
    _this27.initSubset(options);

    // Initialize the metadata
    _this27.info = {
      Producer: 'PDFKit',
      Creator: 'PDFKit',
      CreationDate: new Date()
    };
    if (_this27.options.info) {
      for (var key in _this27.options.info) {
        var val = _this27.options.info[key];
        _this27.info[key] = val;
      }
    }
    if (_this27.options.displayTitle) {
      _this27._root.data.ViewerPreferences = _this27.ref({
        DisplayDocTitle: true
      });
    }

    // Generate file ID
    _this27._id = PDFSecurity.generateFileID(_this27.info);

    // Initialize security settings
    _this27._security = PDFSecurity.create(_this27, options);

    // Write the header
    // PDF version
    _this27._write("%PDF-" + _this27.version);

    // 4 binary chars, as recommended by the spec
    _this27._write('%\xFF\xFF\xFF\xFF');

    // Add the first page
    if (_this27.options.autoFirstPage !== false) {
      _this27.addPage();
    }
    return _this27;
  }
  _inheritsLoose(PDFDocument, _stream$Readable);
  var _proto24 = PDFDocument.prototype;
  _proto24.addPage = function addPage(options) {
    if (options == null) {
      options = this.options;
    }

    // end the current page if needed
    if (!this.options.bufferPages) {
      this.flushPages();
    }

    // create a page object
    this.page = new PDFPage(this, options);
    this._pageBuffer.push(this.page);

    // add the page to the object store
    var pages = this._root.data.Pages.data;
    pages.Kids.push(this.page.dictionary);
    pages.Count++;

    // reset x and y coordinates
    this.x = this.page.margins.left;
    this.y = this.page.margins.top;

    // flip PDF coordinate system so that the origin is in
    // the top left rather than the bottom left
    this._ctm = [1, 0, 0, 1, 0, 0];
    this.transform(1, 0, 0, -1, 0, this.page.height);
    this.emit('pageAdded');
    return this;
  };
  _proto24.continueOnNewPage = function continueOnNewPage(options) {
    var pageMarkings = this.endPageMarkings(this.page);
    this.addPage(options);
    this.initPageMarkings(pageMarkings);
    return this;
  };
  _proto24.bufferedPageRange = function bufferedPageRange() {
    return {
      start: this._pageBufferStart,
      count: this._pageBuffer.length
    };
  };
  _proto24.switchToPage = function switchToPage(n) {
    var page;
    if (!(page = this._pageBuffer[n - this._pageBufferStart])) {
      throw new Error("switchToPage(" + n + ") out of bounds, current buffer covers pages " + this._pageBufferStart + " to " + (this._pageBufferStart + this._pageBuffer.length - 1));
    }
    return this.page = page;
  };
  _proto24.flushPages = function flushPages() {
    // this local variable exists so we're future-proof against
    // reentrant calls to flushPages.
    var pages = this._pageBuffer;
    this._pageBuffer = [];
    this._pageBufferStart += pages.length;
    for (var _iterator12 = _createForOfIteratorHelperLoose(pages), _step12; !(_step12 = _iterator12()).done;) {
      var page = _step12.value;
      this.endPageMarkings(page);
      page.end();
    }
  };
  _proto24.addNamedDestination = function addNamedDestination(name) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    if (args.length === 0) {
      args = ['XYZ', null, null, null];
    }
    if (args[0] === 'XYZ' && args[2] !== null) {
      args[2] = this.page.height - args[2];
    }
    args.unshift(this.page.dictionary);
    this._root.data.Names.data.Dests.add(name, args);
  };
  _proto24.addNamedEmbeddedFile = function addNamedEmbeddedFile(name, ref) {
    if (!this._root.data.Names.data.EmbeddedFiles) {
      // disabling /Limits for this tree fixes attachments not showing in Adobe Reader
      this._root.data.Names.data.EmbeddedFiles = new PDFNameTree({
        limits: false
      });
    }

    // add filespec to EmbeddedFiles
    this._root.data.Names.data.EmbeddedFiles.add(name, ref);
  };
  _proto24.addNamedJavaScript = function addNamedJavaScript(name, js) {
    if (!this._root.data.Names.data.JavaScript) {
      this._root.data.Names.data.JavaScript = new PDFNameTree();
    }
    var data = {
      JS: new String(js),
      S: 'JavaScript'
    };
    this._root.data.Names.data.JavaScript.add(name, data);
  };
  _proto24.ref = function ref(data) {
    var ref = new PDFReference(this, this._offsets.length + 1, data);
    this._offsets.push(null); // placeholder for this object's offset once it is finalized
    this._waiting++;
    return ref;
  };
  _proto24._read = function _read() {}
  // do nothing, but this method is required by node
  ;
  _proto24._write = function _write(data) {
    if (!Buffer.isBuffer(data)) {
      data = Buffer.from(data + '\n', 'binary');
    }
    this.push(data);
    return this._offset += data.length;
  };
  _proto24.addContent = function addContent(data) {
    this.page.write(data);
    return this;
  };
  _proto24._refEnd = function _refEnd(ref) {
    this._offsets[ref.id - 1] = ref.offset;
    if (--this._waiting === 0 && this._ended) {
      this._finalize();
      return this._ended = false;
    }
  };
  _proto24.end = function end() {
    this.flushPages();
    this._info = this.ref();
    for (var key in this.info) {
      var val = this.info[key];
      if (typeof val === 'string') {
        val = new String(val);
      }
      var entry = this.ref(val);
      entry.end();
      this._info.data[key] = entry;
    }
    this._info.end();
    for (var name in this._fontFamilies) {
      var font = this._fontFamilies[name];
      font.finalize();
    }
    this.endOutline();
    this.endMarkings();
    if (this.subset) {
      this.endSubset();
    }
    this.endMetadata();
    this._root.end();
    this._root.data.Pages.end();
    this._root.data.Names.end();
    this.endAcroForm();
    if (this._root.data.ViewerPreferences) {
      this._root.data.ViewerPreferences.end();
    }
    if (this._security) {
      this._security.end();
    }
    if (this._waiting === 0) {
      return this._finalize();
    } else {
      return this._ended = true;
    }
  };
  _proto24._finalize = function _finalize() {
    // generate xref
    var xRefOffset = this._offset;
    this._write('xref');
    this._write("0 " + (this._offsets.length + 1));
    this._write('0000000000 65535 f ');
    for (var _iterator13 = _createForOfIteratorHelperLoose(this._offsets), _step13; !(_step13 = _iterator13()).done;) {
      var offset = _step13.value;
      offset = ("0000000000" + offset).slice(-10);
      this._write(offset + ' 00000 n ');
    }

    // trailer
    var trailer = {
      Size: this._offsets.length + 1,
      Root: this._root,
      Info: this._info,
      ID: [this._id, this._id]
    };
    if (this._security) {
      trailer.Encrypt = this._security.dictionary;
    }
    this._write('trailer');
    this._write(PDFObject.convert(trailer));
    this._write('startxref');
    this._write("" + xRefOffset);
    this._write('%%EOF');

    // end the stream
    return this.push(null);
  };
  _proto24.toString = function toString() {
    return '[object PDFDocument]';
  };
  return PDFDocument;
}(_stream.default.Readable);
var mixin = function mixin(methods) {
  Object.assign(PDFDocument.prototype, methods);
};
mixin(MetadataMixin);
mixin(ColorMixin);
mixin(VectorMixin);
mixin(FontsMixin);
mixin(TextMixin);
mixin(ImagesMixin);
mixin(AnnotationsMixin);
mixin(OutlineMixin);
mixin(MarkingsMixin);
mixin(AcroFormMixin);
mixin(AttachmentsMixin);
mixin(SubsetMixin);
PDFDocument.LineWrapper = LineWrapper;
var _default = exports["default"] = PDFDocument;

/***/ }),

/***/ 73751:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var ___EXPOSE_LOADER_IMPORT___ = __webpack_require__(45314);
var ___EXPOSE_LOADER_GET_GLOBAL_THIS___ = __webpack_require__(72022);
var ___EXPOSE_LOADER_GLOBAL_THIS___ = ___EXPOSE_LOADER_GET_GLOBAL_THIS___;
if (typeof ___EXPOSE_LOADER_GLOBAL_THIS___["pdfMake"] === 'undefined') ___EXPOSE_LOADER_GLOBAL_THIS___["pdfMake"] = ___EXPOSE_LOADER_IMPORT___;
module.exports = ___EXPOSE_LOADER_IMPORT___;


/***/ }),

/***/ 80182:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
// Currently in sync with Node.js lib/assert.js
// https://github.com/nodejs/node/commit/2a51ae424a513ec9a6aa3466baa0cc1d55dd4f3b

// Originally from narwhal.js (http://narwhaljs.org)
// Copyright (c) 2009 Thomas Robinson <280north.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the 'Software'), to
// deal in the Software without restriction, including without limitation the
// rights to use, copy, modify, merge, publish, distribute, sublicense, and/or
// sell copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED 'AS IS', WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN
// ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
// WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.



function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor); } }
function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
var _require = __webpack_require__(35403),
  _require$codes = _require.codes,
  ERR_AMBIGUOUS_ARGUMENT = _require$codes.ERR_AMBIGUOUS_ARGUMENT,
  ERR_INVALID_ARG_TYPE = _require$codes.ERR_INVALID_ARG_TYPE,
  ERR_INVALID_ARG_VALUE = _require$codes.ERR_INVALID_ARG_VALUE,
  ERR_INVALID_RETURN_VALUE = _require$codes.ERR_INVALID_RETURN_VALUE,
  ERR_MISSING_ARGS = _require$codes.ERR_MISSING_ARGS;
var AssertionError = __webpack_require__(85416);
var _require2 = __webpack_require__(7187),
  inspect = _require2.inspect;
var _require$types = (__webpack_require__(7187).types),
  isPromise = _require$types.isPromise,
  isRegExp = _require$types.isRegExp;
var objectAssign = __webpack_require__(37596)();
var objectIs = __webpack_require__(89636)();
var RegExpPrototypeTest = __webpack_require__(67913)('RegExp.prototype.test');
var errorCache = new Map();
var isDeepEqual;
var isDeepStrictEqual;
var parseExpressionAt;
var findNodeAround;
var decoder;
function lazyLoadComparison() {
  var comparison = __webpack_require__(86781);
  isDeepEqual = comparison.isDeepEqual;
  isDeepStrictEqual = comparison.isDeepStrictEqual;
}

// Escape control characters but not \n and \t to keep the line breaks and
// indentation intact.
// eslint-disable-next-line no-control-regex
var escapeSequencesRegExp = /[\x00-\x08\x0b\x0c\x0e-\x1f]/g;
var meta = (/* unused pure expression or super */ null && (["\\u0000", "\\u0001", "\\u0002", "\\u0003", "\\u0004", "\\u0005", "\\u0006", "\\u0007", '\\b', '', '', "\\u000b", '\\f', '', "\\u000e", "\\u000f", "\\u0010", "\\u0011", "\\u0012", "\\u0013", "\\u0014", "\\u0015", "\\u0016", "\\u0017", "\\u0018", "\\u0019", "\\u001a", "\\u001b", "\\u001c", "\\u001d", "\\u001e", "\\u001f"]));
var escapeFn = function escapeFn(str) {
  return meta[str.charCodeAt(0)];
};
var warned = false;

// The assert module provides functions that throw
// AssertionError's when particular conditions are not met. The
// assert module must conform to the following interface.

var assert = module.exports = ok;
var NO_EXCEPTION_SENTINEL = {};

// All of the following functions must throw an AssertionError
// when a corresponding condition is not met, with a message that
// may be undefined if not provided. All assertion methods provide
// both the actual and expected values to the assertion error for
// display purposes.

function innerFail(obj) {
  if (obj.message instanceof Error) throw obj.message;
  throw new AssertionError(obj);
}
function fail(actual, expected, message, operator, stackStartFn) {
  var argsLen = arguments.length;
  var internalMessage;
  if (argsLen === 0) {
    internalMessage = 'Failed';
  } else if (argsLen === 1) {
    message = actual;
    actual = undefined;
  } else {
    if (warned === false) {
      warned = true;
      var warn = process.emitWarning ? process.emitWarning : console.warn.bind(console);
      warn('assert.fail() with more than one argument is deprecated. ' + 'Please use assert.strictEqual() instead or only pass a message.', 'DeprecationWarning', 'DEP0094');
    }
    if (argsLen === 2) operator = '!=';
  }
  if (message instanceof Error) throw message;
  var errArgs = {
    actual: actual,
    expected: expected,
    operator: operator === undefined ? 'fail' : operator,
    stackStartFn: stackStartFn || fail
  };
  if (message !== undefined) {
    errArgs.message = message;
  }
  var err = new AssertionError(errArgs);
  if (internalMessage) {
    err.message = internalMessage;
    err.generatedMessage = true;
  }
  throw err;
}
assert.fail = fail;

// The AssertionError is defined in internal/error.
assert.AssertionError = AssertionError;
function innerOk(fn, argLen, value, message) {
  if (!value) {
    var generatedMessage = false;
    if (argLen === 0) {
      generatedMessage = true;
      message = 'No value argument passed to `assert.ok()`';
    } else if (message instanceof Error) {
      throw message;
    }
    var err = new AssertionError({
      actual: value,
      expected: true,
      message: message,
      operator: '==',
      stackStartFn: fn
    });
    err.generatedMessage = generatedMessage;
    throw err;
  }
}

// Pure assertion tests whether a value is truthy, as determined
// by !!value.
function ok() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  innerOk.apply(void 0, [ok, args.length].concat(args));
}
assert.ok = ok;

// The equality assertion tests shallow, coercive equality with ==.
/* eslint-disable no-restricted-properties */
assert.equal = function equal(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  // eslint-disable-next-line eqeqeq
  if (actual != expected) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: '==',
      stackStartFn: equal
    });
  }
};

// The non-equality assertion tests for whether two objects are not
// equal with !=.
assert.notEqual = function notEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  // eslint-disable-next-line eqeqeq
  if (actual == expected) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: '!=',
      stackStartFn: notEqual
    });
  }
};

// The equivalence assertion tests a deep equality relation.
assert.deepEqual = function deepEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (isDeepEqual === undefined) lazyLoadComparison();
  if (!isDeepEqual(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'deepEqual',
      stackStartFn: deepEqual
    });
  }
};

// The non-equivalence assertion tests for any deep inequality.
assert.notDeepEqual = function notDeepEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (isDeepEqual === undefined) lazyLoadComparison();
  if (isDeepEqual(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'notDeepEqual',
      stackStartFn: notDeepEqual
    });
  }
};
/* eslint-enable */

assert.deepStrictEqual = function deepStrictEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (isDeepEqual === undefined) lazyLoadComparison();
  if (!isDeepStrictEqual(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'deepStrictEqual',
      stackStartFn: deepStrictEqual
    });
  }
};
assert.notDeepStrictEqual = notDeepStrictEqual;
function notDeepStrictEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (isDeepEqual === undefined) lazyLoadComparison();
  if (isDeepStrictEqual(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'notDeepStrictEqual',
      stackStartFn: notDeepStrictEqual
    });
  }
}
assert.strictEqual = function strictEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (!objectIs(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'strictEqual',
      stackStartFn: strictEqual
    });
  }
};
assert.notStrictEqual = function notStrictEqual(actual, expected, message) {
  if (arguments.length < 2) {
    throw new ERR_MISSING_ARGS('actual', 'expected');
  }
  if (objectIs(actual, expected)) {
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: 'notStrictEqual',
      stackStartFn: notStrictEqual
    });
  }
};
var Comparison = /*#__PURE__*/_createClass(function Comparison(obj, keys, actual) {
  var _this = this;
  _classCallCheck(this, Comparison);
  keys.forEach(function (key) {
    if (key in obj) {
      if (actual !== undefined && typeof actual[key] === 'string' && isRegExp(obj[key]) && RegExpPrototypeTest(obj[key], actual[key])) {
        _this[key] = actual[key];
      } else {
        _this[key] = obj[key];
      }
    }
  });
});
function compareExceptionKey(actual, expected, key, message, keys, fn) {
  if (!(key in actual) || !isDeepStrictEqual(actual[key], expected[key])) {
    if (!message) {
      // Create placeholder objects to create a nice output.
      var a = new Comparison(actual, keys);
      var b = new Comparison(expected, keys, actual);
      var err = new AssertionError({
        actual: a,
        expected: b,
        operator: 'deepStrictEqual',
        stackStartFn: fn
      });
      err.actual = actual;
      err.expected = expected;
      err.operator = fn.name;
      throw err;
    }
    innerFail({
      actual: actual,
      expected: expected,
      message: message,
      operator: fn.name,
      stackStartFn: fn
    });
  }
}
function expectedException(actual, expected, msg, fn) {
  if (typeof expected !== 'function') {
    if (isRegExp(expected)) return RegExpPrototypeTest(expected, actual);
    // assert.doesNotThrow does not accept objects.
    if (arguments.length === 2) {
      throw new ERR_INVALID_ARG_TYPE('expected', ['Function', 'RegExp'], expected);
    }

    // Handle primitives properly.
    if (_typeof(actual) !== 'object' || actual === null) {
      var err = new AssertionError({
        actual: actual,
        expected: expected,
        message: msg,
        operator: 'deepStrictEqual',
        stackStartFn: fn
      });
      err.operator = fn.name;
      throw err;
    }
    var keys = Object.keys(expected);
    // Special handle errors to make sure the name and the message are compared
    // as well.
    if (expected instanceof Error) {
      keys.push('name', 'message');
    } else if (keys.length === 0) {
      throw new ERR_INVALID_ARG_VALUE('error', expected, 'may not be an empty object');
    }
    if (isDeepEqual === undefined) lazyLoadComparison();
    keys.forEach(function (key) {
      if (typeof actual[key] === 'string' && isRegExp(expected[key]) && RegExpPrototypeTest(expected[key], actual[key])) {
        return;
      }
      compareExceptionKey(actual, expected, key, msg, keys, fn);
    });
    return true;
  }
  // Guard instanceof against arrow functions as they don't have a prototype.
  if (expected.prototype !== undefined && actual instanceof expected) {
    return true;
  }
  if (Error.isPrototypeOf(expected)) {
    return false;
  }
  return expected.call({}, actual) === true;
}
function getActual(fn) {
  if (typeof fn !== 'function') {
    throw new ERR_INVALID_ARG_TYPE('fn', 'Function', fn);
  }
  try {
    fn();
  } catch (e) {
    return e;
  }
  return NO_EXCEPTION_SENTINEL;
}
function checkIsPromise(obj) {
  // Accept native ES6 promises and promises that are implemented in a similar
  // way. Do not accept thenables that use a function as `obj` and that have no
  // `catch` handler.

  // TODO: thenables are checked up until they have the correct methods,
  // but according to documentation, the `then` method should receive
  // the `fulfill` and `reject` arguments as well or it may be never resolved.

  return isPromise(obj) || obj !== null && _typeof(obj) === 'object' && typeof obj.then === 'function' && typeof obj.catch === 'function';
}
function waitForActual(promiseFn) {
  return Promise.resolve().then(function () {
    var resultPromise;
    if (typeof promiseFn === 'function') {
      // Return a rejected promise if `promiseFn` throws synchronously.
      resultPromise = promiseFn();
      // Fail in case no promise is returned.
      if (!checkIsPromise(resultPromise)) {
        throw new ERR_INVALID_RETURN_VALUE('instance of Promise', 'promiseFn', resultPromise);
      }
    } else if (checkIsPromise(promiseFn)) {
      resultPromise = promiseFn;
    } else {
      throw new ERR_INVALID_ARG_TYPE('promiseFn', ['Function', 'Promise'], promiseFn);
    }
    return Promise.resolve().then(function () {
      return resultPromise;
    }).then(function () {
      return NO_EXCEPTION_SENTINEL;
    }).catch(function (e) {
      return e;
    });
  });
}
function expectsError(stackStartFn, actual, error, message) {
  if (typeof error === 'string') {
    if (arguments.length === 4) {
      throw new ERR_INVALID_ARG_TYPE('error', ['Object', 'Error', 'Function', 'RegExp'], error);
    }
    if (_typeof(actual) === 'object' && actual !== null) {
      if (actual.message === error) {
        throw new ERR_AMBIGUOUS_ARGUMENT('error/message', "The error message \"".concat(actual.message, "\" is identical to the message."));
      }
    } else if (actual === error) {
      throw new ERR_AMBIGUOUS_ARGUMENT('error/message', "The error \"".concat(actual, "\" is identical to the message."));
    }
    message = error;
    error = undefined;
  } else if (error != null && _typeof(error) !== 'object' && typeof error !== 'function') {
    throw new ERR_INVALID_ARG_TYPE('error', ['Object', 'Error', 'Function', 'RegExp'], error);
  }
  if (actual === NO_EXCEPTION_SENTINEL) {
    var details = '';
    if (error && error.name) {
      details += " (".concat(error.name, ")");
    }
    details += message ? ": ".concat(message) : '.';
    var fnType = stackStartFn.name === 'rejects' ? 'rejection' : 'exception';
    innerFail({
      actual: undefined,
      expected: error,
      operator: stackStartFn.name,
      message: "Missing expected ".concat(fnType).concat(details),
      stackStartFn: stackStartFn
    });
  }
  if (error && !expectedException(actual, error, message, stackStartFn)) {
    throw actual;
  }
}
function expectsNoError(stackStartFn, actual, error, message) {
  if (actual === NO_EXCEPTION_SENTINEL) return;
  if (typeof error === 'string') {
    message = error;
    error = undefined;
  }
  if (!error || expectedException(actual, error)) {
    var details = message ? ": ".concat(message) : '.';
    var fnType = stackStartFn.name === 'doesNotReject' ? 'rejection' : 'exception';
    innerFail({
      actual: actual,
      expected: error,
      operator: stackStartFn.name,
      message: "Got unwanted ".concat(fnType).concat(details, "\n") + "Actual message: \"".concat(actual && actual.message, "\""),
      stackStartFn: stackStartFn
    });
  }
  throw actual;
}
assert.throws = function throws(promiseFn) {
  for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    args[_key2 - 1] = arguments[_key2];
  }
  expectsError.apply(void 0, [throws, getActual(promiseFn)].concat(args));
};
assert.rejects = function rejects(promiseFn) {
  for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    args[_key3 - 1] = arguments[_key3];
  }
  return waitForActual(promiseFn).then(function (result) {
    return expectsError.apply(void 0, [rejects, result].concat(args));
  });
};
assert.doesNotThrow = function doesNotThrow(fn) {
  for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
    args[_key4 - 1] = arguments[_key4];
  }
  expectsNoError.apply(void 0, [doesNotThrow, getActual(fn)].concat(args));
};
assert.doesNotReject = function doesNotReject(fn) {
  for (var _len5 = arguments.length, args = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
    args[_key5 - 1] = arguments[_key5];
  }
  return waitForActual(fn).then(function (result) {
    return expectsNoError.apply(void 0, [doesNotReject, result].concat(args));
  });
};
assert.ifError = function ifError(err) {
  if (err !== null && err !== undefined) {
    var message = 'ifError got unwanted exception: ';
    if (_typeof(err) === 'object' && typeof err.message === 'string') {
      if (err.message.length === 0 && err.constructor) {
        message += err.constructor.name;
      } else {
        message += err.message;
      }
    } else {
      message += inspect(err);
    }
    var newErr = new AssertionError({
      actual: err,
      expected: null,
      operator: 'ifError',
      message: message,
      stackStartFn: ifError
    });

    // Make sure we actually have a stack trace!
    var origStack = err.stack;
    if (typeof origStack === 'string') {
      // This will remove any duplicated frames from the error frames taken
      // from within `ifError` and add the original error frames to the newly
      // created ones.
      var tmp2 = origStack.split('\n');
      tmp2.shift();
      // Filter all frames existing in err.stack.
      var tmp1 = newErr.stack.split('\n');
      for (var i = 0; i < tmp2.length; i++) {
        // Find the first occurrence of the frame.
        var pos = tmp1.indexOf(tmp2[i]);
        if (pos !== -1) {
          // Only keep new frames.
          tmp1 = tmp1.slice(0, pos);
          break;
        }
      }
      newErr.stack = "".concat(tmp1.join('\n'), "\n").concat(tmp2.join('\n'));
    }
    throw newErr;
  }
};

// Currently in sync with Node.js lib/assert.js
// https://github.com/nodejs/node/commit/2a871df3dfb8ea663ef5e1f8f62701ec51384ecb
function internalMatch(string, regexp, message, fn, fnName) {
  if (!isRegExp(regexp)) {
    throw new ERR_INVALID_ARG_TYPE('regexp', 'RegExp', regexp);
  }
  var match = fnName === 'match';
  if (typeof string !== 'string' || RegExpPrototypeTest(regexp, string) !== match) {
    if (message instanceof Error) {
      throw message;
    }
    var generatedMessage = !message;

    // 'The input was expected to not match the regular expression ' +
    message = message || (typeof string !== 'string' ? 'The "string" argument must be of type string. Received type ' + "".concat(_typeof(string), " (").concat(inspect(string), ")") : (match ? 'The input did not match the regular expression ' : 'The input was expected to not match the regular expression ') + "".concat(inspect(regexp), ". Input:\n\n").concat(inspect(string), "\n"));
    var err = new AssertionError({
      actual: string,
      expected: regexp,
      message: message,
      operator: fnName,
      stackStartFn: fn
    });
    err.generatedMessage = generatedMessage;
    throw err;
  }
}
assert.match = function match(string, regexp, message) {
  internalMatch(string, regexp, message, match, 'match');
};
assert.doesNotMatch = function doesNotMatch(string, regexp, message) {
  internalMatch(string, regexp, message, doesNotMatch, 'doesNotMatch');
};

// Expose a strict only variant of assert
function strict() {
  for (var _len6 = arguments.length, args = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
    args[_key6] = arguments[_key6];
  }
  innerOk.apply(void 0, [strict, args.length].concat(args));
}
assert.strict = objectAssign(strict, assert, {
  equal: assert.strictEqual,
  deepEqual: assert.deepStrictEqual,
  notEqual: assert.notStrictEqual,
  notDeepEqual: assert.notDeepStrictEqual
});
assert.strict.strict = assert.strict;

/***/ }),

/***/ 85416:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
// Currently in sync with Node.js lib/internal/assert/assertion_error.js
// https://github.com/nodejs/node/commit/0817840f775032169ddd70c85ac059f18ffcc81c



function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor); } }
function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }
function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }
function _wrapNativeSuper(Class) { var _cache = typeof Map === "function" ? new Map() : undefined; _wrapNativeSuper = function _wrapNativeSuper(Class) { if (Class === null || !_isNativeFunction(Class)) return Class; if (typeof Class !== "function") { throw new TypeError("Super expression must either be null or a function"); } if (typeof _cache !== "undefined") { if (_cache.has(Class)) return _cache.get(Class); _cache.set(Class, Wrapper); } function Wrapper() { return _construct(Class, arguments, _getPrototypeOf(this).constructor); } Wrapper.prototype = Object.create(Class.prototype, { constructor: { value: Wrapper, enumerable: false, writable: true, configurable: true } }); return _setPrototypeOf(Wrapper, Class); }; return _wrapNativeSuper(Class); }
function _construct(Parent, args, Class) { if (_isNativeReflectConstruct()) { _construct = Reflect.construct.bind(); } else { _construct = function _construct(Parent, args, Class) { var a = [null]; a.push.apply(a, args); var Constructor = Function.bind.apply(Parent, a); var instance = new Constructor(); if (Class) _setPrototypeOf(instance, Class.prototype); return instance; }; } return _construct.apply(null, arguments); }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
function _isNativeFunction(fn) { return Function.toString.call(fn).indexOf("[native code]") !== -1; }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
var _require = __webpack_require__(7187),
  inspect = _require.inspect;
var _require2 = __webpack_require__(35403),
  ERR_INVALID_ARG_TYPE = _require2.codes.ERR_INVALID_ARG_TYPE;

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith
function endsWith(str, search, this_len) {
  if (this_len === undefined || this_len > str.length) {
    this_len = str.length;
  }
  return str.substring(this_len - search.length, this_len) === search;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/repeat
function repeat(str, count) {
  count = Math.floor(count);
  if (str.length == 0 || count == 0) return '';
  var maxCount = str.length * count;
  count = Math.floor(Math.log(count) / Math.log(2));
  while (count) {
    str += str;
    count--;
  }
  str += str.substring(0, maxCount - str.length);
  return str;
}
var blue = '';
var green = '';
var red = '';
var white = '';
var kReadableOperator = {
  deepStrictEqual: 'Expected values to be strictly deep-equal:',
  strictEqual: 'Expected values to be strictly equal:',
  strictEqualObject: 'Expected "actual" to be reference-equal to "expected":',
  deepEqual: 'Expected values to be loosely deep-equal:',
  equal: 'Expected values to be loosely equal:',
  notDeepStrictEqual: 'Expected "actual" not to be strictly deep-equal to:',
  notStrictEqual: 'Expected "actual" to be strictly unequal to:',
  notStrictEqualObject: 'Expected "actual" not to be reference-equal to "expected":',
  notDeepEqual: 'Expected "actual" not to be loosely deep-equal to:',
  notEqual: 'Expected "actual" to be loosely unequal to:',
  notIdentical: 'Values identical but not reference-equal:'
};

// Comparing short primitives should just show === / !== instead of using the
// diff.
var kMaxShortLength = 10;
function copyError(source) {
  var keys = Object.keys(source);
  var target = Object.create(Object.getPrototypeOf(source));
  keys.forEach(function (key) {
    target[key] = source[key];
  });
  Object.defineProperty(target, 'message', {
    value: source.message
  });
  return target;
}
function inspectValue(val) {
  // The util.inspect default values could be changed. This makes sure the
  // error messages contain the necessary information nevertheless.
  return inspect(val, {
    compact: false,
    customInspect: false,
    depth: 1000,
    maxArrayLength: Infinity,
    // Assert compares only enumerable properties (with a few exceptions).
    showHidden: false,
    // Having a long line as error is better than wrapping the line for
    // comparison for now.
    // TODO(BridgeAR): `breakLength` should be limited as soon as soon as we
    // have meta information about the inspected properties (i.e., know where
    // in what line the property starts and ends).
    breakLength: Infinity,
    // Assert does not detect proxies currently.
    showProxy: false,
    sorted: true,
    // Inspect getters as we also check them when comparing entries.
    getters: true
  });
}
function createErrDiff(actual, expected, operator) {
  var other = '';
  var res = '';
  var lastPos = 0;
  var end = '';
  var skipped = false;
  var actualInspected = inspectValue(actual);
  var actualLines = actualInspected.split('\n');
  var expectedLines = inspectValue(expected).split('\n');
  var i = 0;
  var indicator = '';

  // In case both values are objects explicitly mark them as not reference equal
  // for the `strictEqual` operator.
  if (operator === 'strictEqual' && _typeof(actual) === 'object' && _typeof(expected) === 'object' && actual !== null && expected !== null) {
    operator = 'strictEqualObject';
  }

  // If "actual" and "expected" fit on a single line and they are not strictly
  // equal, check further special handling.
  if (actualLines.length === 1 && expectedLines.length === 1 && actualLines[0] !== expectedLines[0]) {
    var inputLength = actualLines[0].length + expectedLines[0].length;
    // If the character length of "actual" and "expected" together is less than
    // kMaxShortLength and if neither is an object and at least one of them is
    // not `zero`, use the strict equal comparison to visualize the output.
    if (inputLength <= kMaxShortLength) {
      if ((_typeof(actual) !== 'object' || actual === null) && (_typeof(expected) !== 'object' || expected === null) && (actual !== 0 || expected !== 0)) {
        // -0 === +0
        return "".concat(kReadableOperator[operator], "\n\n") + "".concat(actualLines[0], " !== ").concat(expectedLines[0], "\n");
      }
    } else if (operator !== 'strictEqualObject') {
      // If the stderr is a tty and the input length is lower than the current
      // columns per line, add a mismatch indicator below the output. If it is
      // not a tty, use a default value of 80 characters.
      var maxLength = process.stderr && process.stderr.isTTY ? process.stderr.columns : 80;
      if (inputLength < maxLength) {
        while (actualLines[0][i] === expectedLines[0][i]) {
          i++;
        }
        // Ignore the first characters.
        if (i > 2) {
          // Add position indicator for the first mismatch in case it is a
          // single line and the input length is less than the column length.
          indicator = "\n  ".concat(repeat(' ', i), "^");
          i = 0;
        }
      }
    }
  }

  // Remove all ending lines that match (this optimizes the output for
  // readability by reducing the number of total changed lines).
  var a = actualLines[actualLines.length - 1];
  var b = expectedLines[expectedLines.length - 1];
  while (a === b) {
    if (i++ < 2) {
      end = "\n  ".concat(a).concat(end);
    } else {
      other = a;
    }
    actualLines.pop();
    expectedLines.pop();
    if (actualLines.length === 0 || expectedLines.length === 0) break;
    a = actualLines[actualLines.length - 1];
    b = expectedLines[expectedLines.length - 1];
  }
  var maxLines = Math.max(actualLines.length, expectedLines.length);
  // Strict equal with identical objects that are not identical by reference.
  // E.g., assert.deepStrictEqual({ a: Symbol() }, { a: Symbol() })
  if (maxLines === 0) {
    // We have to get the result again. The lines were all removed before.
    var _actualLines = actualInspected.split('\n');

    // Only remove lines in case it makes sense to collapse those.
    // TODO: Accept env to always show the full error.
    if (_actualLines.length > 30) {
      _actualLines[26] = "".concat(blue, "...").concat(white);
      while (_actualLines.length > 27) {
        _actualLines.pop();
      }
    }
    return "".concat(kReadableOperator.notIdentical, "\n\n").concat(_actualLines.join('\n'), "\n");
  }
  if (i > 3) {
    end = "\n".concat(blue, "...").concat(white).concat(end);
    skipped = true;
  }
  if (other !== '') {
    end = "\n  ".concat(other).concat(end);
    other = '';
  }
  var printedLines = 0;
  var msg = kReadableOperator[operator] + "\n".concat(green, "+ actual").concat(white, " ").concat(red, "- expected").concat(white);
  var skippedMsg = " ".concat(blue, "...").concat(white, " Lines skipped");
  for (i = 0; i < maxLines; i++) {
    // Only extra expected lines exist
    var cur = i - lastPos;
    if (actualLines.length < i + 1) {
      // If the last diverging line is more than one line above and the
      // current line is at least line three, add some of the former lines and
      // also add dots to indicate skipped entries.
      if (cur > 1 && i > 2) {
        if (cur > 4) {
          res += "\n".concat(blue, "...").concat(white);
          skipped = true;
        } else if (cur > 3) {
          res += "\n  ".concat(expectedLines[i - 2]);
          printedLines++;
        }
        res += "\n  ".concat(expectedLines[i - 1]);
        printedLines++;
      }
      // Mark the current line as the last diverging one.
      lastPos = i;
      // Add the expected line to the cache.
      other += "\n".concat(red, "-").concat(white, " ").concat(expectedLines[i]);
      printedLines++;
      // Only extra actual lines exist
    } else if (expectedLines.length < i + 1) {
      // If the last diverging line is more than one line above and the
      // current line is at least line three, add some of the former lines and
      // also add dots to indicate skipped entries.
      if (cur > 1 && i > 2) {
        if (cur > 4) {
          res += "\n".concat(blue, "...").concat(white);
          skipped = true;
        } else if (cur > 3) {
          res += "\n  ".concat(actualLines[i - 2]);
          printedLines++;
        }
        res += "\n  ".concat(actualLines[i - 1]);
        printedLines++;
      }
      // Mark the current line as the last diverging one.
      lastPos = i;
      // Add the actual line to the result.
      res += "\n".concat(green, "+").concat(white, " ").concat(actualLines[i]);
      printedLines++;
      // Lines diverge
    } else {
      var expectedLine = expectedLines[i];
      var actualLine = actualLines[i];
      // If the lines diverge, specifically check for lines that only diverge by
      // a trailing comma. In that case it is actually identical and we should
      // mark it as such.
      var divergingLines = actualLine !== expectedLine && (!endsWith(actualLine, ',') || actualLine.slice(0, -1) !== expectedLine);
      // If the expected line has a trailing comma but is otherwise identical,
      // add a comma at the end of the actual line. Otherwise the output could
      // look weird as in:
      //
      //   [
      //     1         // No comma at the end!
      // +   2
      //   ]
      //
      if (divergingLines && endsWith(expectedLine, ',') && expectedLine.slice(0, -1) === actualLine) {
        divergingLines = false;
        actualLine += ',';
      }
      if (divergingLines) {
        // If the last diverging line is more than one line above and the
        // current line is at least line three, add some of the former lines and
        // also add dots to indicate skipped entries.
        if (cur > 1 && i > 2) {
          if (cur > 4) {
            res += "\n".concat(blue, "...").concat(white);
            skipped = true;
          } else if (cur > 3) {
            res += "\n  ".concat(actualLines[i - 2]);
            printedLines++;
          }
          res += "\n  ".concat(actualLines[i - 1]);
          printedLines++;
        }
        // Mark the current line as the last diverging one.
        lastPos = i;
        // Add the actual line to the result and cache the expected diverging
        // line so consecutive diverging lines show up as +++--- and not +-+-+-.
        res += "\n".concat(green, "+").concat(white, " ").concat(actualLine);
        other += "\n".concat(red, "-").concat(white, " ").concat(expectedLine);
        printedLines += 2;
        // Lines are identical
      } else {
        // Add all cached information to the result before adding other things
        // and reset the cache.
        res += other;
        other = '';
        // If the last diverging line is exactly one line above or if it is the
        // very first line, add the line to the result.
        if (cur === 1 || i === 0) {
          res += "\n  ".concat(actualLine);
          printedLines++;
        }
      }
    }
    // Inspected object to big (Show ~20 rows max)
    if (printedLines > 20 && i < maxLines - 2) {
      return "".concat(msg).concat(skippedMsg, "\n").concat(res, "\n").concat(blue, "...").concat(white).concat(other, "\n") + "".concat(blue, "...").concat(white);
    }
  }
  return "".concat(msg).concat(skipped ? skippedMsg : '', "\n").concat(res).concat(other).concat(end).concat(indicator);
}
var AssertionError = /*#__PURE__*/function (_Error, _inspect$custom) {
  _inherits(AssertionError, _Error);
  var _super = _createSuper(AssertionError);
  function AssertionError(options) {
    var _this;
    _classCallCheck(this, AssertionError);
    if (_typeof(options) !== 'object' || options === null) {
      throw new ERR_INVALID_ARG_TYPE('options', 'Object', options);
    }
    var message = options.message,
      operator = options.operator,
      stackStartFn = options.stackStartFn;
    var actual = options.actual,
      expected = options.expected;
    var limit = Error.stackTraceLimit;
    Error.stackTraceLimit = 0;
    if (message != null) {
      _this = _super.call(this, String(message));
    } else {
      if (process.stderr && process.stderr.isTTY) {
        // Reset on each call to make sure we handle dynamically set environment
        // variables correct.
        if (process.stderr && process.stderr.getColorDepth && process.stderr.getColorDepth() !== 1) {
          blue = "\x1B[34m";
          green = "\x1B[32m";
          white = "\x1B[39m";
          red = "\x1B[31m";
        } else {
          blue = '';
          green = '';
          white = '';
          red = '';
        }
      }
      // Prevent the error stack from being visible by duplicating the error
      // in a very close way to the original in case both sides are actually
      // instances of Error.
      if (_typeof(actual) === 'object' && actual !== null && _typeof(expected) === 'object' && expected !== null && 'stack' in actual && actual instanceof Error && 'stack' in expected && expected instanceof Error) {
        actual = copyError(actual);
        expected = copyError(expected);
      }
      if (operator === 'deepStrictEqual' || operator === 'strictEqual') {
        _this = _super.call(this, createErrDiff(actual, expected, operator));
      } else if (operator === 'notDeepStrictEqual' || operator === 'notStrictEqual') {
        // In case the objects are equal but the operator requires unequal, show
        // the first object and say A equals B
        var base = kReadableOperator[operator];
        var res = inspectValue(actual).split('\n');

        // In case "actual" is an object, it should not be reference equal.
        if (operator === 'notStrictEqual' && _typeof(actual) === 'object' && actual !== null) {
          base = kReadableOperator.notStrictEqualObject;
        }

        // Only remove lines in case it makes sense to collapse those.
        // TODO: Accept env to always show the full error.
        if (res.length > 30) {
          res[26] = "".concat(blue, "...").concat(white);
          while (res.length > 27) {
            res.pop();
          }
        }

        // Only print a single input.
        if (res.length === 1) {
          _this = _super.call(this, "".concat(base, " ").concat(res[0]));
        } else {
          _this = _super.call(this, "".concat(base, "\n\n").concat(res.join('\n'), "\n"));
        }
      } else {
        var _res = inspectValue(actual);
        var other = '';
        var knownOperators = kReadableOperator[operator];
        if (operator === 'notDeepEqual' || operator === 'notEqual') {
          _res = "".concat(kReadableOperator[operator], "\n\n").concat(_res);
          if (_res.length > 1024) {
            _res = "".concat(_res.slice(0, 1021), "...");
          }
        } else {
          other = "".concat(inspectValue(expected));
          if (_res.length > 512) {
            _res = "".concat(_res.slice(0, 509), "...");
          }
          if (other.length > 512) {
            other = "".concat(other.slice(0, 509), "...");
          }
          if (operator === 'deepEqual' || operator === 'equal') {
            _res = "".concat(knownOperators, "\n\n").concat(_res, "\n\nshould equal\n\n");
          } else {
            other = " ".concat(operator, " ").concat(other);
          }
        }
        _this = _super.call(this, "".concat(_res).concat(other));
      }
    }
    Error.stackTraceLimit = limit;
    _this.generatedMessage = !message;
    Object.defineProperty(_assertThisInitialized(_this), 'name', {
      value: 'AssertionError [ERR_ASSERTION]',
      enumerable: false,
      writable: true,
      configurable: true
    });
    _this.code = 'ERR_ASSERTION';
    _this.actual = actual;
    _this.expected = expected;
    _this.operator = operator;
    if (Error.captureStackTrace) {
      // eslint-disable-next-line no-restricted-syntax
      Error.captureStackTrace(_assertThisInitialized(_this), stackStartFn);
    }
    // Create error message including the error code in the name.
    _this.stack;
    // Reset the name.
    _this.name = 'AssertionError';
    return _possibleConstructorReturn(_this);
  }
  _createClass(AssertionError, [{
    key: "toString",
    value: function toString() {
      return "".concat(this.name, " [").concat(this.code, "]: ").concat(this.message);
    }
  }, {
    key: _inspect$custom,
    value: function value(recurseTimes, ctx) {
      // This limits the `actual` and `expected` property default inspection to
      // the minimum depth. Otherwise those values would be too verbose compared
      // to the actual error message which contains a combined view of these two
      // input values.
      return inspect(this, _objectSpread(_objectSpread({}, ctx), {}, {
        customInspect: false,
        depth: 0
      }));
    }
  }]);
  return AssertionError;
}( /*#__PURE__*/_wrapNativeSuper(Error), inspect.custom);
module.exports = AssertionError;

/***/ }),

/***/ 35403:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Currently in sync with Node.js lib/internal/errors.js
// https://github.com/nodejs/node/commit/3b044962c48fe313905877a96b5d0894a5404f6f

/* eslint node-core/documented-errors: "error" */
/* eslint node-core/alphabetize-errors: "error" */
/* eslint node-core/prefer-util-format-errors: "error" */



// The whole point behind this internal module is to allow Node.js to no
// longer be forced to treat every error message change as a semver-major
// change. The NodeError classes here all expose a `code` property whose
// value statically and permanently identifies the error. While the error
// message may change, the code should not.
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor); } }
function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }
function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }
var codes = {};

// Lazy loaded
var assert;
var util;
function createErrorType(code, message, Base) {
  if (!Base) {
    Base = Error;
  }
  function getMessage(arg1, arg2, arg3) {
    if (typeof message === 'string') {
      return message;
    } else {
      return message(arg1, arg2, arg3);
    }
  }
  var NodeError = /*#__PURE__*/function (_Base) {
    _inherits(NodeError, _Base);
    var _super = _createSuper(NodeError);
    function NodeError(arg1, arg2, arg3) {
      var _this;
      _classCallCheck(this, NodeError);
      _this = _super.call(this, getMessage(arg1, arg2, arg3));
      _this.code = code;
      return _this;
    }
    return _createClass(NodeError);
  }(Base);
  codes[code] = NodeError;
}

// https://github.com/nodejs/node/blob/v10.8.0/lib/internal/errors.js
function oneOf(expected, thing) {
  if (Array.isArray(expected)) {
    var len = expected.length;
    expected = expected.map(function (i) {
      return String(i);
    });
    if (len > 2) {
      return "one of ".concat(thing, " ").concat(expected.slice(0, len - 1).join(', '), ", or ") + expected[len - 1];
    } else if (len === 2) {
      return "one of ".concat(thing, " ").concat(expected[0], " or ").concat(expected[1]);
    } else {
      return "of ".concat(thing, " ").concat(expected[0]);
    }
  } else {
    return "of ".concat(thing, " ").concat(String(expected));
  }
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith
function startsWith(str, search, pos) {
  return str.substr(!pos || pos < 0 ? 0 : +pos, search.length) === search;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith
function endsWith(str, search, this_len) {
  if (this_len === undefined || this_len > str.length) {
    this_len = str.length;
  }
  return str.substring(this_len - search.length, this_len) === search;
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
function includes(str, search, start) {
  if (typeof start !== 'number') {
    start = 0;
  }
  if (start + search.length > str.length) {
    return false;
  } else {
    return str.indexOf(search, start) !== -1;
  }
}
createErrorType('ERR_AMBIGUOUS_ARGUMENT', 'The "%s" argument is ambiguous. %s', TypeError);
createErrorType('ERR_INVALID_ARG_TYPE', function (name, expected, actual) {
  if (assert === undefined) assert = __webpack_require__(80182);
  assert(typeof name === 'string', "'name' must be a string");

  // determiner: 'must be' or 'must not be'
  var determiner;
  if (typeof expected === 'string' && startsWith(expected, 'not ')) {
    determiner = 'must not be';
    expected = expected.replace(/^not /, '');
  } else {
    determiner = 'must be';
  }
  var msg;
  if (endsWith(name, ' argument')) {
    // For cases like 'first argument'
    msg = "The ".concat(name, " ").concat(determiner, " ").concat(oneOf(expected, 'type'));
  } else {
    var type = includes(name, '.') ? 'property' : 'argument';
    msg = "The \"".concat(name, "\" ").concat(type, " ").concat(determiner, " ").concat(oneOf(expected, 'type'));
  }

  // TODO(BridgeAR): Improve the output by showing `null` and similar.
  msg += ". Received type ".concat(_typeof(actual));
  return msg;
}, TypeError);
createErrorType('ERR_INVALID_ARG_VALUE', function (name, value) {
  var reason = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'is invalid';
  if (util === undefined) util = __webpack_require__(7187);
  var inspected = util.inspect(value);
  if (inspected.length > 128) {
    inspected = "".concat(inspected.slice(0, 128), "...");
  }
  return "The argument '".concat(name, "' ").concat(reason, ". Received ").concat(inspected);
}, TypeError, RangeError);
createErrorType('ERR_INVALID_RETURN_VALUE', function (input, name, value) {
  var type;
  if (value && value.constructor && value.constructor.name) {
    type = "instance of ".concat(value.constructor.name);
  } else {
    type = "type ".concat(_typeof(value));
  }
  return "Expected ".concat(input, " to be returned from the \"").concat(name, "\"") + " function but got ".concat(type, ".");
}, TypeError);
createErrorType('ERR_MISSING_ARGS', function () {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  if (assert === undefined) assert = __webpack_require__(80182);
  assert(args.length > 0, 'At least one arg needs to be specified');
  var msg = 'The ';
  var len = args.length;
  args = args.map(function (a) {
    return "\"".concat(a, "\"");
  });
  switch (len) {
    case 1:
      msg += "".concat(args[0], " argument");
      break;
    case 2:
      msg += "".concat(args[0], " and ").concat(args[1], " arguments");
      break;
    default:
      msg += args.slice(0, len - 1).join(', ');
      msg += ", and ".concat(args[len - 1], " arguments");
      break;
  }
  return "".concat(msg, " must be specified");
}, TypeError);
module.exports.codes = codes;

/***/ }),

/***/ 86781:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Currently in sync with Node.js lib/internal/util/comparisons.js
// https://github.com/nodejs/node/commit/112cc7c27551254aa2b17098fb774867f05ed0d9



function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
var regexFlagsSupported = /a/g.flags !== undefined;
var arrayFromSet = function arrayFromSet(set) {
  var array = [];
  set.forEach(function (value) {
    return array.push(value);
  });
  return array;
};
var arrayFromMap = function arrayFromMap(map) {
  var array = [];
  map.forEach(function (value, key) {
    return array.push([key, value]);
  });
  return array;
};
var objectIs = Object.is ? Object.is : __webpack_require__(98527);
var objectGetOwnPropertySymbols = Object.getOwnPropertySymbols ? Object.getOwnPropertySymbols : function () {
  return [];
};
var numberIsNaN = Number.isNaN ? Number.isNaN : __webpack_require__(7051);
function uncurryThis(f) {
  return f.call.bind(f);
}
var hasOwnProperty = uncurryThis(Object.prototype.hasOwnProperty);
var propertyIsEnumerable = uncurryThis(Object.prototype.propertyIsEnumerable);
var objectToString = uncurryThis(Object.prototype.toString);
var _require$types = (__webpack_require__(7187).types),
  isAnyArrayBuffer = _require$types.isAnyArrayBuffer,
  isArrayBufferView = _require$types.isArrayBufferView,
  isDate = _require$types.isDate,
  isMap = _require$types.isMap,
  isRegExp = _require$types.isRegExp,
  isSet = _require$types.isSet,
  isNativeError = _require$types.isNativeError,
  isBoxedPrimitive = _require$types.isBoxedPrimitive,
  isNumberObject = _require$types.isNumberObject,
  isStringObject = _require$types.isStringObject,
  isBooleanObject = _require$types.isBooleanObject,
  isBigIntObject = _require$types.isBigIntObject,
  isSymbolObject = _require$types.isSymbolObject,
  isFloat32Array = _require$types.isFloat32Array,
  isFloat64Array = _require$types.isFloat64Array;
function isNonIndex(key) {
  if (key.length === 0 || key.length > 10) return true;
  for (var i = 0; i < key.length; i++) {
    var code = key.charCodeAt(i);
    if (code < 48 || code > 57) return true;
  }
  // The maximum size for an array is 2 ** 32 -1.
  return key.length === 10 && key >= Math.pow(2, 32);
}
function getOwnNonIndexProperties(value) {
  return Object.keys(value).filter(isNonIndex).concat(objectGetOwnPropertySymbols(value).filter(Object.prototype.propertyIsEnumerable.bind(value)));
}

// Taken from https://github.com/feross/buffer/blob/680e9e5e488f22aac27599a57dc844a6315928dd/index.js
// original notice:
/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <feross@feross.org> <http://feross.org>
 * @license  MIT
 */
function compare(a, b) {
  if (a === b) {
    return 0;
  }
  var x = a.length;
  var y = b.length;
  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i];
      y = b[i];
      break;
    }
  }
  if (x < y) {
    return -1;
  }
  if (y < x) {
    return 1;
  }
  return 0;
}
var ONLY_ENUMERABLE = undefined;
var kStrict = true;
var kLoose = false;
var kNoIterator = 0;
var kIsArray = 1;
var kIsSet = 2;
var kIsMap = 3;

// Check if they have the same source and flags
function areSimilarRegExps(a, b) {
  return regexFlagsSupported ? a.source === b.source && a.flags === b.flags : RegExp.prototype.toString.call(a) === RegExp.prototype.toString.call(b);
}
function areSimilarFloatArrays(a, b) {
  if (a.byteLength !== b.byteLength) {
    return false;
  }
  for (var offset = 0; offset < a.byteLength; offset++) {
    if (a[offset] !== b[offset]) {
      return false;
    }
  }
  return true;
}
function areSimilarTypedArrays(a, b) {
  if (a.byteLength !== b.byteLength) {
    return false;
  }
  return compare(new Uint8Array(a.buffer, a.byteOffset, a.byteLength), new Uint8Array(b.buffer, b.byteOffset, b.byteLength)) === 0;
}
function areEqualArrayBuffers(buf1, buf2) {
  return buf1.byteLength === buf2.byteLength && compare(new Uint8Array(buf1), new Uint8Array(buf2)) === 0;
}
function isEqualBoxedPrimitive(val1, val2) {
  if (isNumberObject(val1)) {
    return isNumberObject(val2) && objectIs(Number.prototype.valueOf.call(val1), Number.prototype.valueOf.call(val2));
  }
  if (isStringObject(val1)) {
    return isStringObject(val2) && String.prototype.valueOf.call(val1) === String.prototype.valueOf.call(val2);
  }
  if (isBooleanObject(val1)) {
    return isBooleanObject(val2) && Boolean.prototype.valueOf.call(val1) === Boolean.prototype.valueOf.call(val2);
  }
  if (isBigIntObject(val1)) {
    return isBigIntObject(val2) && BigInt.prototype.valueOf.call(val1) === BigInt.prototype.valueOf.call(val2);
  }
  return isSymbolObject(val2) && Symbol.prototype.valueOf.call(val1) === Symbol.prototype.valueOf.call(val2);
}

// Notes: Type tags are historical [[Class]] properties that can be set by
// FunctionTemplate::SetClassName() in C++ or Symbol.toStringTag in JS
// and retrieved using Object.prototype.toString.call(obj) in JS
// See https://tc39.github.io/ecma262/#sec-object.prototype.tostring
// for a list of tags pre-defined in the spec.
// There are some unspecified tags in the wild too (e.g. typed array tags).
// Since tags can be altered, they only serve fast failures
//
// Typed arrays and buffers are checked by comparing the content in their
// underlying ArrayBuffer. This optimization requires that it's
// reasonable to interpret their underlying memory in the same way,
// which is checked by comparing their type tags.
// (e.g. a Uint8Array and a Uint16Array with the same memory content
// could still be different because they will be interpreted differently).
//
// For strict comparison, objects should have
// a) The same built-in type tags
// b) The same prototypes.

function innerDeepEqual(val1, val2, strict, memos) {
  // All identical values are equivalent, as determined by ===.
  if (val1 === val2) {
    if (val1 !== 0) return true;
    return strict ? objectIs(val1, val2) : true;
  }

  // Check more closely if val1 and val2 are equal.
  if (strict) {
    if (_typeof(val1) !== 'object') {
      return typeof val1 === 'number' && numberIsNaN(val1) && numberIsNaN(val2);
    }
    if (_typeof(val2) !== 'object' || val1 === null || val2 === null) {
      return false;
    }
    if (Object.getPrototypeOf(val1) !== Object.getPrototypeOf(val2)) {
      return false;
    }
  } else {
    if (val1 === null || _typeof(val1) !== 'object') {
      if (val2 === null || _typeof(val2) !== 'object') {
        // eslint-disable-next-line eqeqeq
        return val1 == val2;
      }
      return false;
    }
    if (val2 === null || _typeof(val2) !== 'object') {
      return false;
    }
  }
  var val1Tag = objectToString(val1);
  var val2Tag = objectToString(val2);
  if (val1Tag !== val2Tag) {
    return false;
  }
  if (Array.isArray(val1)) {
    // Check for sparse arrays and general fast path
    if (val1.length !== val2.length) {
      return false;
    }
    var keys1 = getOwnNonIndexProperties(val1, ONLY_ENUMERABLE);
    var keys2 = getOwnNonIndexProperties(val2, ONLY_ENUMERABLE);
    if (keys1.length !== keys2.length) {
      return false;
    }
    return keyCheck(val1, val2, strict, memos, kIsArray, keys1);
  }
  // [browserify] This triggers on certain types in IE (Map/Set) so we don't
  // wan't to early return out of the rest of the checks. However we can check
  // if the second value is one of these values and the first isn't.
  if (val1Tag === '[object Object]') {
    // return keyCheck(val1, val2, strict, memos, kNoIterator);
    if (!isMap(val1) && isMap(val2) || !isSet(val1) && isSet(val2)) {
      return false;
    }
  }
  if (isDate(val1)) {
    if (!isDate(val2) || Date.prototype.getTime.call(val1) !== Date.prototype.getTime.call(val2)) {
      return false;
    }
  } else if (isRegExp(val1)) {
    if (!isRegExp(val2) || !areSimilarRegExps(val1, val2)) {
      return false;
    }
  } else if (isNativeError(val1) || val1 instanceof Error) {
    // Do not compare the stack as it might differ even though the error itself
    // is otherwise identical.
    if (val1.message !== val2.message || val1.name !== val2.name) {
      return false;
    }
  } else if (isArrayBufferView(val1)) {
    if (!strict && (isFloat32Array(val1) || isFloat64Array(val1))) {
      if (!areSimilarFloatArrays(val1, val2)) {
        return false;
      }
    } else if (!areSimilarTypedArrays(val1, val2)) {
      return false;
    }
    // Buffer.compare returns true, so val1.length === val2.length. If they both
    // only contain numeric keys, we don't need to exam further than checking
    // the symbols.
    var _keys = getOwnNonIndexProperties(val1, ONLY_ENUMERABLE);
    var _keys2 = getOwnNonIndexProperties(val2, ONLY_ENUMERABLE);
    if (_keys.length !== _keys2.length) {
      return false;
    }
    return keyCheck(val1, val2, strict, memos, kNoIterator, _keys);
  } else if (isSet(val1)) {
    if (!isSet(val2) || val1.size !== val2.size) {
      return false;
    }
    return keyCheck(val1, val2, strict, memos, kIsSet);
  } else if (isMap(val1)) {
    if (!isMap(val2) || val1.size !== val2.size) {
      return false;
    }
    return keyCheck(val1, val2, strict, memos, kIsMap);
  } else if (isAnyArrayBuffer(val1)) {
    if (!areEqualArrayBuffers(val1, val2)) {
      return false;
    }
  } else if (isBoxedPrimitive(val1) && !isEqualBoxedPrimitive(val1, val2)) {
    return false;
  }
  return keyCheck(val1, val2, strict, memos, kNoIterator);
}
function getEnumerables(val, keys) {
  return keys.filter(function (k) {
    return propertyIsEnumerable(val, k);
  });
}
function keyCheck(val1, val2, strict, memos, iterationType, aKeys) {
  // For all remaining Object pairs, including Array, objects and Maps,
  // equivalence is determined by having:
  // a) The same number of owned enumerable properties
  // b) The same set of keys/indexes (although not necessarily the same order)
  // c) Equivalent values for every corresponding key/index
  // d) For Sets and Maps, equal contents
  // Note: this accounts for both named and indexed properties on Arrays.
  if (arguments.length === 5) {
    aKeys = Object.keys(val1);
    var bKeys = Object.keys(val2);

    // The pair must have the same number of owned properties.
    if (aKeys.length !== bKeys.length) {
      return false;
    }
  }

  // Cheap key test
  var i = 0;
  for (; i < aKeys.length; i++) {
    if (!hasOwnProperty(val2, aKeys[i])) {
      return false;
    }
  }
  if (strict && arguments.length === 5) {
    var symbolKeysA = objectGetOwnPropertySymbols(val1);
    if (symbolKeysA.length !== 0) {
      var count = 0;
      for (i = 0; i < symbolKeysA.length; i++) {
        var key = symbolKeysA[i];
        if (propertyIsEnumerable(val1, key)) {
          if (!propertyIsEnumerable(val2, key)) {
            return false;
          }
          aKeys.push(key);
          count++;
        } else if (propertyIsEnumerable(val2, key)) {
          return false;
        }
      }
      var symbolKeysB = objectGetOwnPropertySymbols(val2);
      if (symbolKeysA.length !== symbolKeysB.length && getEnumerables(val2, symbolKeysB).length !== count) {
        return false;
      }
    } else {
      var _symbolKeysB = objectGetOwnPropertySymbols(val2);
      if (_symbolKeysB.length !== 0 && getEnumerables(val2, _symbolKeysB).length !== 0) {
        return false;
      }
    }
  }
  if (aKeys.length === 0 && (iterationType === kNoIterator || iterationType === kIsArray && val1.length === 0 || val1.size === 0)) {
    return true;
  }

  // Use memos to handle cycles.
  if (memos === undefined) {
    memos = {
      val1: new Map(),
      val2: new Map(),
      position: 0
    };
  } else {
    // We prevent up to two map.has(x) calls by directly retrieving the value
    // and checking for undefined. The map can only contain numbers, so it is
    // safe to check for undefined only.
    var val2MemoA = memos.val1.get(val1);
    if (val2MemoA !== undefined) {
      var val2MemoB = memos.val2.get(val2);
      if (val2MemoB !== undefined) {
        return val2MemoA === val2MemoB;
      }
    }
    memos.position++;
  }
  memos.val1.set(val1, memos.position);
  memos.val2.set(val2, memos.position);
  var areEq = objEquiv(val1, val2, strict, aKeys, memos, iterationType);
  memos.val1.delete(val1);
  memos.val2.delete(val2);
  return areEq;
}
function setHasEqualElement(set, val1, strict, memo) {
  // Go looking.
  var setValues = arrayFromSet(set);
  for (var i = 0; i < setValues.length; i++) {
    var val2 = setValues[i];
    if (innerDeepEqual(val1, val2, strict, memo)) {
      // Remove the matching element to make sure we do not check that again.
      set.delete(val2);
      return true;
    }
  }
  return false;
}

// See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness#Loose_equality_using
// Sadly it is not possible to detect corresponding values properly in case the
// type is a string, number, bigint or boolean. The reason is that those values
// can match lots of different string values (e.g., 1n == '+00001').
function findLooseMatchingPrimitives(prim) {
  switch (_typeof(prim)) {
    case 'undefined':
      return null;
    case 'object':
      // Only pass in null as object!
      return undefined;
    case 'symbol':
      return false;
    case 'string':
      prim = +prim;
    // Loose equal entries exist only if the string is possible to convert to
    // a regular number and not NaN.
    // Fall through
    case 'number':
      if (numberIsNaN(prim)) {
        return false;
      }
  }
  return true;
}
function setMightHaveLoosePrim(a, b, prim) {
  var altValue = findLooseMatchingPrimitives(prim);
  if (altValue != null) return altValue;
  return b.has(altValue) && !a.has(altValue);
}
function mapMightHaveLoosePrim(a, b, prim, item, memo) {
  var altValue = findLooseMatchingPrimitives(prim);
  if (altValue != null) {
    return altValue;
  }
  var curB = b.get(altValue);
  if (curB === undefined && !b.has(altValue) || !innerDeepEqual(item, curB, false, memo)) {
    return false;
  }
  return !a.has(altValue) && innerDeepEqual(item, curB, false, memo);
}
function setEquiv(a, b, strict, memo) {
  // This is a lazily initiated Set of entries which have to be compared
  // pairwise.
  var set = null;
  var aValues = arrayFromSet(a);
  for (var i = 0; i < aValues.length; i++) {
    var val = aValues[i];
    // Note: Checking for the objects first improves the performance for object
    // heavy sets but it is a minor slow down for primitives. As they are fast
    // to check this improves the worst case scenario instead.
    if (_typeof(val) === 'object' && val !== null) {
      if (set === null) {
        set = new Set();
      }
      // If the specified value doesn't exist in the second set its an not null
      // object (or non strict only: a not matching primitive) we'll need to go
      // hunting for something thats deep-(strict-)equal to it. To make this
      // O(n log n) complexity we have to copy these values in a new set first.
      set.add(val);
    } else if (!b.has(val)) {
      if (strict) return false;

      // Fast path to detect missing string, symbol, undefined and null values.
      if (!setMightHaveLoosePrim(a, b, val)) {
        return false;
      }
      if (set === null) {
        set = new Set();
      }
      set.add(val);
    }
  }
  if (set !== null) {
    var bValues = arrayFromSet(b);
    for (var _i = 0; _i < bValues.length; _i++) {
      var _val = bValues[_i];
      // We have to check if a primitive value is already
      // matching and only if it's not, go hunting for it.
      if (_typeof(_val) === 'object' && _val !== null) {
        if (!setHasEqualElement(set, _val, strict, memo)) return false;
      } else if (!strict && !a.has(_val) && !setHasEqualElement(set, _val, strict, memo)) {
        return false;
      }
    }
    return set.size === 0;
  }
  return true;
}
function mapHasEqualEntry(set, map, key1, item1, strict, memo) {
  // To be able to handle cases like:
  //   Map([[{}, 'a'], [{}, 'b']]) vs Map([[{}, 'b'], [{}, 'a']])
  // ... we need to consider *all* matching keys, not just the first we find.
  var setValues = arrayFromSet(set);
  for (var i = 0; i < setValues.length; i++) {
    var key2 = setValues[i];
    if (innerDeepEqual(key1, key2, strict, memo) && innerDeepEqual(item1, map.get(key2), strict, memo)) {
      set.delete(key2);
      return true;
    }
  }
  return false;
}
function mapEquiv(a, b, strict, memo) {
  var set = null;
  var aEntries = arrayFromMap(a);
  for (var i = 0; i < aEntries.length; i++) {
    var _aEntries$i = _slicedToArray(aEntries[i], 2),
      key = _aEntries$i[0],
      item1 = _aEntries$i[1];
    if (_typeof(key) === 'object' && key !== null) {
      if (set === null) {
        set = new Set();
      }
      set.add(key);
    } else {
      // By directly retrieving the value we prevent another b.has(key) check in
      // almost all possible cases.
      var item2 = b.get(key);
      if (item2 === undefined && !b.has(key) || !innerDeepEqual(item1, item2, strict, memo)) {
        if (strict) return false;
        // Fast path to detect missing string, symbol, undefined and null
        // keys.
        if (!mapMightHaveLoosePrim(a, b, key, item1, memo)) return false;
        if (set === null) {
          set = new Set();
        }
        set.add(key);
      }
    }
  }
  if (set !== null) {
    var bEntries = arrayFromMap(b);
    for (var _i2 = 0; _i2 < bEntries.length; _i2++) {
      var _bEntries$_i = _slicedToArray(bEntries[_i2], 2),
        _key = _bEntries$_i[0],
        item = _bEntries$_i[1];
      if (_typeof(_key) === 'object' && _key !== null) {
        if (!mapHasEqualEntry(set, a, _key, item, strict, memo)) return false;
      } else if (!strict && (!a.has(_key) || !innerDeepEqual(a.get(_key), item, false, memo)) && !mapHasEqualEntry(set, a, _key, item, false, memo)) {
        return false;
      }
    }
    return set.size === 0;
  }
  return true;
}
function objEquiv(a, b, strict, keys, memos, iterationType) {
  // Sets and maps don't have their entries accessible via normal object
  // properties.
  var i = 0;
  if (iterationType === kIsSet) {
    if (!setEquiv(a, b, strict, memos)) {
      return false;
    }
  } else if (iterationType === kIsMap) {
    if (!mapEquiv(a, b, strict, memos)) {
      return false;
    }
  } else if (iterationType === kIsArray) {
    for (; i < a.length; i++) {
      if (hasOwnProperty(a, i)) {
        if (!hasOwnProperty(b, i) || !innerDeepEqual(a[i], b[i], strict, memos)) {
          return false;
        }
      } else if (hasOwnProperty(b, i)) {
        return false;
      } else {
        // Array is sparse.
        var keysA = Object.keys(a);
        for (; i < keysA.length; i++) {
          var key = keysA[i];
          if (!hasOwnProperty(b, key) || !innerDeepEqual(a[key], b[key], strict, memos)) {
            return false;
          }
        }
        if (keysA.length !== Object.keys(b).length) {
          return false;
        }
        return true;
      }
    }
  }

  // The pair must have equivalent values for every corresponding key.
  // Possibly expensive deep test:
  for (i = 0; i < keys.length; i++) {
    var _key2 = keys[i];
    if (!innerDeepEqual(a[_key2], b[_key2], strict, memos)) {
      return false;
    }
  }
  return true;
}
function isDeepEqual(val1, val2) {
  return innerDeepEqual(val1, val2, kLoose);
}
function isDeepStrictEqual(val1, val2) {
  return innerDeepEqual(val1, val2, kStrict);
}
module.exports = {
  isDeepEqual: isDeepEqual,
  isDeepStrictEqual: isDeepStrictEqual
};

/***/ }),

/***/ 32504:
/***/ (function(__unused_webpack_module, exports) {

"use strict";


exports.byteLength = byteLength
exports.toByteArray = toByteArray
exports.fromByteArray = fromByteArray

var lookup = []
var revLookup = []
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array

var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
for (var i = 0, len = code.length; i < len; ++i) {
  lookup[i] = code[i]
  revLookup[code.charCodeAt(i)] = i
}

// Support decoding URL-safe base64 strings, as Node.js does.
// See: https://en.wikipedia.org/wiki/Base64#URL_applications
revLookup['-'.charCodeAt(0)] = 62
revLookup['_'.charCodeAt(0)] = 63

function getLens (b64) {
  var len = b64.length

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  // Trim off extra bytes after placeholder bytes are found
  // See: https://github.com/beatgammit/base64-js/issues/42
  var validLen = b64.indexOf('=')
  if (validLen === -1) validLen = len

  var placeHoldersLen = validLen === len
    ? 0
    : 4 - (validLen % 4)

  return [validLen, placeHoldersLen]
}

// base64 is 4/3 + up to two characters of the original data
function byteLength (b64) {
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function _byteLength (b64, validLen, placeHoldersLen) {
  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
}

function toByteArray (b64) {
  var tmp
  var lens = getLens(b64)
  var validLen = lens[0]
  var placeHoldersLen = lens[1]

  var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen))

  var curByte = 0

  // if there are placeholders, only get up to the last complete 4 chars
  var len = placeHoldersLen > 0
    ? validLen - 4
    : validLen

  var i
  for (i = 0; i < len; i += 4) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 18) |
      (revLookup[b64.charCodeAt(i + 1)] << 12) |
      (revLookup[b64.charCodeAt(i + 2)] << 6) |
      revLookup[b64.charCodeAt(i + 3)]
    arr[curByte++] = (tmp >> 16) & 0xFF
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 2) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 2) |
      (revLookup[b64.charCodeAt(i + 1)] >> 4)
    arr[curByte++] = tmp & 0xFF
  }

  if (placeHoldersLen === 1) {
    tmp =
      (revLookup[b64.charCodeAt(i)] << 10) |
      (revLookup[b64.charCodeAt(i + 1)] << 4) |
      (revLookup[b64.charCodeAt(i + 2)] >> 2)
    arr[curByte++] = (tmp >> 8) & 0xFF
    arr[curByte++] = tmp & 0xFF
  }

  return arr
}

function tripletToBase64 (num) {
  return lookup[num >> 18 & 0x3F] +
    lookup[num >> 12 & 0x3F] +
    lookup[num >> 6 & 0x3F] +
    lookup[num & 0x3F]
}

function encodeChunk (uint8, start, end) {
  var tmp
  var output = []
  for (var i = start; i < end; i += 3) {
    tmp =
      ((uint8[i] << 16) & 0xFF0000) +
      ((uint8[i + 1] << 8) & 0xFF00) +
      (uint8[i + 2] & 0xFF)
    output.push(tripletToBase64(tmp))
  }
  return output.join('')
}

function fromByteArray (uint8) {
  var tmp
  var len = uint8.length
  var extraBytes = len % 3 // if we have 1 byte left, pad 2 bytes
  var parts = []
  var maxChunkLength = 16383 // must be multiple of 3

  // go through the array every three bytes, we'll deal with trailing stuff later
  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(
      uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)
    ))
  }

  // pad the end with zeros, but make sure to not forget the extra bytes
  if (extraBytes === 1) {
    tmp = uint8[len - 1]
    parts.push(
      lookup[tmp >> 2] +
      lookup[(tmp << 4) & 0x3F] +
      '=='
    )
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + uint8[len - 1]
    parts.push(
      lookup[tmp >> 10] +
      lookup[(tmp >> 4) & 0x3F] +
      lookup[(tmp << 2) & 0x3F] +
      '='
    )
  }

  return parts.join('')
}


/***/ }),

/***/ 34097:
/***/ (function(module) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Bit reading helpers
*/

var BROTLI_READ_SIZE = 4096;
var BROTLI_IBUF_SIZE =  (2 * BROTLI_READ_SIZE + 32);
var BROTLI_IBUF_MASK =  (2 * BROTLI_READ_SIZE - 1);

var kBitMask = new Uint32Array([
  0, 1, 3, 7, 15, 31, 63, 127, 255, 511, 1023, 2047, 4095, 8191, 16383, 32767,
  65535, 131071, 262143, 524287, 1048575, 2097151, 4194303, 8388607, 16777215
]);

/* Input byte buffer, consist of a ringbuffer and a "slack" region where */
/* bytes from the start of the ringbuffer are copied. */
function BrotliBitReader(input) {
  this.buf_ = new Uint8Array(BROTLI_IBUF_SIZE);
  this.input_ = input;    /* input callback */
  
  this.reset();
}

BrotliBitReader.READ_SIZE = BROTLI_READ_SIZE;
BrotliBitReader.IBUF_MASK = BROTLI_IBUF_MASK;

BrotliBitReader.prototype.reset = function() {
  this.buf_ptr_ = 0;      /* next input will write here */
  this.val_ = 0;          /* pre-fetched bits */
  this.pos_ = 0;          /* byte position in stream */
  this.bit_pos_ = 0;      /* current bit-reading position in val_ */
  this.bit_end_pos_ = 0;  /* bit-reading end position from LSB of val_ */
  this.eos_ = 0;          /* input stream is finished */
  
  this.readMoreInput();
  for (var i = 0; i < 4; i++) {
    this.val_ |= this.buf_[this.pos_] << (8 * i);
    ++this.pos_;
  }
  
  return this.bit_end_pos_ > 0;
};

/* Fills up the input ringbuffer by calling the input callback.

   Does nothing if there are at least 32 bytes present after current position.

   Returns 0 if either:
    - the input callback returned an error, or
    - there is no more input and the position is past the end of the stream.

   After encountering the end of the input stream, 32 additional zero bytes are
   copied to the ringbuffer, therefore it is safe to call this function after
   every 32 bytes of input is read.
*/
BrotliBitReader.prototype.readMoreInput = function() {
  if (this.bit_end_pos_ > 256) {
    return;
  } else if (this.eos_) {
    if (this.bit_pos_ > this.bit_end_pos_)
      throw new Error('Unexpected end of input ' + this.bit_pos_ + ' ' + this.bit_end_pos_);
  } else {
    var dst = this.buf_ptr_;
    var bytes_read = this.input_.read(this.buf_, dst, BROTLI_READ_SIZE);
    if (bytes_read < 0) {
      throw new Error('Unexpected end of input');
    }
    
    if (bytes_read < BROTLI_READ_SIZE) {
      this.eos_ = 1;
      /* Store 32 bytes of zero after the stream end. */
      for (var p = 0; p < 32; p++)
        this.buf_[dst + bytes_read + p] = 0;
    }
    
    if (dst === 0) {
      /* Copy the head of the ringbuffer to the slack region. */
      for (var p = 0; p < 32; p++)
        this.buf_[(BROTLI_READ_SIZE << 1) + p] = this.buf_[p];

      this.buf_ptr_ = BROTLI_READ_SIZE;
    } else {
      this.buf_ptr_ = 0;
    }
    
    this.bit_end_pos_ += bytes_read << 3;
  }
};

/* Guarantees that there are at least 24 bits in the buffer. */
BrotliBitReader.prototype.fillBitWindow = function() {    
  while (this.bit_pos_ >= 8) {
    this.val_ >>>= 8;
    this.val_ |= this.buf_[this.pos_ & BROTLI_IBUF_MASK] << 24;
    ++this.pos_;
    this.bit_pos_ = this.bit_pos_ - 8 >>> 0;
    this.bit_end_pos_ = this.bit_end_pos_ - 8 >>> 0;
  }
};

/* Reads the specified number of bits from Read Buffer. */
BrotliBitReader.prototype.readBits = function(n_bits) {
  if (32 - this.bit_pos_ < n_bits) {
    this.fillBitWindow();
  }
  
  var val = ((this.val_ >>> this.bit_pos_) & kBitMask[n_bits]);
  this.bit_pos_ += n_bits;
  return val;
};

module.exports = BrotliBitReader;


/***/ }),

/***/ 7043:
/***/ (function(__unused_webpack_module, exports) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Lookup table to map the previous two bytes to a context id.

   There are four different context modeling modes defined here:
     CONTEXT_LSB6: context id is the least significant 6 bits of the last byte,
     CONTEXT_MSB6: context id is the most significant 6 bits of the last byte,
     CONTEXT_UTF8: second-order context model tuned for UTF8-encoded text,
     CONTEXT_SIGNED: second-order context model tuned for signed integers.

   The context id for the UTF8 context model is calculated as follows. If p1
   and p2 are the previous two bytes, we calcualte the context as

     context = kContextLookup[p1] | kContextLookup[p2 + 256].

   If the previous two bytes are ASCII characters (i.e. < 128), this will be
   equivalent to

     context = 4 * context1(p1) + context2(p2),

   where context1 is based on the previous byte in the following way:

     0  : non-ASCII control
     1  : \t, \n, \r
     2  : space
     3  : other punctuation
     4  : " '
     5  : %
     6  : ( < [ {
     7  : ) > ] }
     8  : , ; :
     9  : .
     10 : =
     11 : number
     12 : upper-case vowel
     13 : upper-case consonant
     14 : lower-case vowel
     15 : lower-case consonant

   and context2 is based on the second last byte:

     0 : control, space
     1 : punctuation
     2 : upper-case letter, number
     3 : lower-case letter

   If the last byte is ASCII, and the second last byte is not (in a valid UTF8
   stream it will be a continuation byte, value between 128 and 191), the
   context is the same as if the second last byte was an ASCII control or space.

   If the last byte is a UTF8 lead byte (value >= 192), then the next byte will
   be a continuation byte and the context id is 2 or 3 depending on the LSB of
   the last byte and to a lesser extent on the second last byte if it is ASCII.

   If the last byte is a UTF8 continuation byte, the second last byte can be:
     - continuation byte: the next byte is probably ASCII or lead byte (assuming
       4-byte UTF8 characters are rare) and the context id is 0 or 1.
     - lead byte (192 - 207): next byte is ASCII or lead byte, context is 0 or 1
     - lead byte (208 - 255): next byte is continuation byte, context is 2 or 3

   The possible value combinations of the previous two bytes, the range of
   context ids and the type of the next byte is summarized in the table below:

   |--------\-----------------------------------------------------------------|
   |         \                         Last byte                              |
   | Second   \---------------------------------------------------------------|
   | last byte \    ASCII            |   cont. byte        |   lead byte      |
   |            \   (0-127)          |   (128-191)         |   (192-)         |
   |=============|===================|=====================|==================|
   |  ASCII      | next: ASCII/lead  |  not valid          |  next: cont.     |
   |  (0-127)    | context: 4 - 63   |                     |  context: 2 - 3  |
   |-------------|-------------------|---------------------|------------------|
   |  cont. byte | next: ASCII/lead  |  next: ASCII/lead   |  next: cont.     |
   |  (128-191)  | context: 4 - 63   |  context: 0 - 1     |  context: 2 - 3  |
   |-------------|-------------------|---------------------|------------------|
   |  lead byte  | not valid         |  next: ASCII/lead   |  not valid       |
   |  (192-207)  |                   |  context: 0 - 1     |                  |
   |-------------|-------------------|---------------------|------------------|
   |  lead byte  | not valid         |  next: cont.        |  not valid       |
   |  (208-)     |                   |  context: 2 - 3     |                  |
   |-------------|-------------------|---------------------|------------------|

   The context id for the signed context mode is calculated as:

     context = (kContextLookup[512 + p1] << 3) | kContextLookup[512 + p2].

   For any context modeling modes, the context ids can be calculated by |-ing
   together two lookups from one table using context model dependent offsets:

     context = kContextLookup[offset1 + p1] | kContextLookup[offset2 + p2].

   where offset1 and offset2 are dependent on the context mode.
*/

var CONTEXT_LSB6         = 0;
var CONTEXT_MSB6         = 1;
var CONTEXT_UTF8         = 2;
var CONTEXT_SIGNED       = 3;

/* Common context lookup table for all context modes. */
exports.lookup = new Uint8Array([
  /* CONTEXT_UTF8, last byte. */
  /* ASCII range. */
   0,  0,  0,  0,  0,  0,  0,  0,  0,  4,  4,  0,  0,  4,  0,  0,
   0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,  0,
   8, 12, 16, 12, 12, 20, 12, 16, 24, 28, 12, 12, 32, 12, 36, 12,
  44, 44, 44, 44, 44, 44, 44, 44, 44, 44, 32, 32, 24, 40, 28, 12,
  12, 48, 52, 52, 52, 48, 52, 52, 52, 48, 52, 52, 52, 52, 52, 48,
  52, 52, 52, 52, 52, 48, 52, 52, 52, 52, 52, 24, 12, 28, 12, 12,
  12, 56, 60, 60, 60, 56, 60, 60, 60, 56, 60, 60, 60, 60, 60, 56,
  60, 60, 60, 60, 60, 56, 60, 60, 60, 60, 60, 24, 12, 28, 12,  0,
  /* UTF8 continuation byte range. */
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1,
  /* UTF8 lead byte range. */
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3, 2, 3,
  /* CONTEXT_UTF8 second last byte. */
  /* ASCII range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1, 1,
  1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1, 1, 1, 1, 1,
  1, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 1, 1, 1, 1, 0,
  /* UTF8 continuation byte range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  /* UTF8 lead byte range. */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  /* CONTEXT_SIGNED, second last byte. */
  0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3, 3,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5, 5,
  6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 7,
  /* CONTEXT_SIGNED, last byte, same as the above values shifted by 3 bits. */
   0, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16, 16,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32, 32,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40,
  48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 48, 56,
  /* CONTEXT_LSB6, last byte. */
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
   0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11, 12, 13, 14, 15,
  16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
  32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
  48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63,
  /* CONTEXT_MSB6, last byte. */
   0,  0,  0,  0,  1,  1,  1,  1,  2,  2,  2,  2,  3,  3,  3,  3,
   4,  4,  4,  4,  5,  5,  5,  5,  6,  6,  6,  6,  7,  7,  7,  7,
   8,  8,  8,  8,  9,  9,  9,  9, 10, 10, 10, 10, 11, 11, 11, 11,
  12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15,
  16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18, 19, 19, 19, 19,
  20, 20, 20, 20, 21, 21, 21, 21, 22, 22, 22, 22, 23, 23, 23, 23,
  24, 24, 24, 24, 25, 25, 25, 25, 26, 26, 26, 26, 27, 27, 27, 27,
  28, 28, 28, 28, 29, 29, 29, 29, 30, 30, 30, 30, 31, 31, 31, 31,
  32, 32, 32, 32, 33, 33, 33, 33, 34, 34, 34, 34, 35, 35, 35, 35,
  36, 36, 36, 36, 37, 37, 37, 37, 38, 38, 38, 38, 39, 39, 39, 39,
  40, 40, 40, 40, 41, 41, 41, 41, 42, 42, 42, 42, 43, 43, 43, 43,
  44, 44, 44, 44, 45, 45, 45, 45, 46, 46, 46, 46, 47, 47, 47, 47,
  48, 48, 48, 48, 49, 49, 49, 49, 50, 50, 50, 50, 51, 51, 51, 51,
  52, 52, 52, 52, 53, 53, 53, 53, 54, 54, 54, 54, 55, 55, 55, 55,
  56, 56, 56, 56, 57, 57, 57, 57, 58, 58, 58, 58, 59, 59, 59, 59,
  60, 60, 60, 60, 61, 61, 61, 61, 62, 62, 62, 62, 63, 63, 63, 63,
  /* CONTEXT_{M,L}SB6, second last byte, */
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
]);

exports.lookupOffsets = new Uint16Array([
  /* CONTEXT_LSB6 */
  1024, 1536,
  /* CONTEXT_MSB6 */
  1280, 1536,
  /* CONTEXT_UTF8 */
  0, 256,
  /* CONTEXT_SIGNED */
  768, 512,
]);


/***/ }),

/***/ 20980:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var __webpack_unused_export__;
/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/

var BrotliInput = (__webpack_require__(98197)/* .BrotliInput */ .z);
var BrotliOutput = (__webpack_require__(98197)/* .BrotliOutput */ .y);
var BrotliBitReader = __webpack_require__(34097);
var BrotliDictionary = __webpack_require__(80614);
var HuffmanCode = (__webpack_require__(81561)/* .HuffmanCode */ .z);
var BrotliBuildHuffmanTable = (__webpack_require__(81561)/* .BrotliBuildHuffmanTable */ .u);
var Context = __webpack_require__(7043);
var Prefix = __webpack_require__(42210);
var Transform = __webpack_require__(87984);

var kDefaultCodeLength = 8;
var kCodeLengthRepeatCode = 16;
var kNumLiteralCodes = 256;
var kNumInsertAndCopyCodes = 704;
var kNumBlockLengthCodes = 26;
var kLiteralContextBits = 6;
var kDistanceContextBits = 2;

var HUFFMAN_TABLE_BITS = 8;
var HUFFMAN_TABLE_MASK = 0xff;
/* Maximum possible Huffman table size for an alphabet size of 704, max code
 * length 15 and root table bits 8. */
var HUFFMAN_MAX_TABLE_SIZE = 1080;

var CODE_LENGTH_CODES = 18;
var kCodeLengthCodeOrder = new Uint8Array([
  1, 2, 3, 4, 0, 5, 17, 6, 16, 7, 8, 9, 10, 11, 12, 13, 14, 15,
]);

var NUM_DISTANCE_SHORT_CODES = 16;
var kDistanceShortCodeIndexOffset = new Uint8Array([
  3, 2, 1, 0, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 2
]);

var kDistanceShortCodeValueOffset = new Int8Array([
  0, 0, 0, 0, -1, 1, -2, 2, -3, 3, -1, 1, -2, 2, -3, 3
]);

var kMaxHuffmanTableSize = new Uint16Array([
  256, 402, 436, 468, 500, 534, 566, 598, 630, 662, 694, 726, 758, 790, 822,
  854, 886, 920, 952, 984, 1016, 1048, 1080
]);

function DecodeWindowBits(br) {
  var n;
  if (br.readBits(1) === 0) {
    return 16;
  }
  
  n = br.readBits(3);
  if (n > 0) {
    return 17 + n;
  }
  
  n = br.readBits(3);
  if (n > 0) {
    return 8 + n;
  }
  
  return 17;
}

/* Decodes a number in the range [0..255], by reading 1 - 11 bits. */
function DecodeVarLenUint8(br) {
  if (br.readBits(1)) {
    var nbits = br.readBits(3);
    if (nbits === 0) {
      return 1;
    } else {
      return br.readBits(nbits) + (1 << nbits);
    }
  }
  return 0;
}

function MetaBlockLength() {
  this.meta_block_length = 0;
  this.input_end = 0;
  this.is_uncompressed = 0;
  this.is_metadata = false;
}

function DecodeMetaBlockLength(br) {
  var out = new MetaBlockLength;  
  var size_nibbles;
  var size_bytes;
  var i;
  
  out.input_end = br.readBits(1);
  if (out.input_end && br.readBits(1)) {
    return out;
  }
  
  size_nibbles = br.readBits(2) + 4;
  if (size_nibbles === 7) {
    out.is_metadata = true;
    
    if (br.readBits(1) !== 0)
      throw new Error('Invalid reserved bit');
    
    size_bytes = br.readBits(2);
    if (size_bytes === 0)
      return out;
    
    for (i = 0; i < size_bytes; i++) {
      var next_byte = br.readBits(8);
      if (i + 1 === size_bytes && size_bytes > 1 && next_byte === 0)
        throw new Error('Invalid size byte');
      
      out.meta_block_length |= next_byte << (i * 8);
    }
  } else {
    for (i = 0; i < size_nibbles; ++i) {
      var next_nibble = br.readBits(4);
      if (i + 1 === size_nibbles && size_nibbles > 4 && next_nibble === 0)
        throw new Error('Invalid size nibble');
      
      out.meta_block_length |= next_nibble << (i * 4);
    }
  }
  
  ++out.meta_block_length;
  
  if (!out.input_end && !out.is_metadata) {
    out.is_uncompressed = br.readBits(1);
  }
  
  return out;
}

/* Decodes the next Huffman code from bit-stream. */
function ReadSymbol(table, index, br) {
  var start_index = index;
  
  var nbits;
  br.fillBitWindow();
  index += (br.val_ >>> br.bit_pos_) & HUFFMAN_TABLE_MASK;
  nbits = table[index].bits - HUFFMAN_TABLE_BITS;
  if (nbits > 0) {
    br.bit_pos_ += HUFFMAN_TABLE_BITS;
    index += table[index].value;
    index += (br.val_ >>> br.bit_pos_) & ((1 << nbits) - 1);
  }
  br.bit_pos_ += table[index].bits;
  return table[index].value;
}

function ReadHuffmanCodeLengths(code_length_code_lengths, num_symbols, code_lengths, br) {
  var symbol = 0;
  var prev_code_len = kDefaultCodeLength;
  var repeat = 0;
  var repeat_code_len = 0;
  var space = 32768;
  
  var table = [];
  for (var i = 0; i < 32; i++)
    table.push(new HuffmanCode(0, 0));
  
  BrotliBuildHuffmanTable(table, 0, 5, code_length_code_lengths, CODE_LENGTH_CODES);

  while (symbol < num_symbols && space > 0) {
    var p = 0;
    var code_len;
    
    br.readMoreInput();
    br.fillBitWindow();
    p += (br.val_ >>> br.bit_pos_) & 31;
    br.bit_pos_ += table[p].bits;
    code_len = table[p].value & 0xff;
    if (code_len < kCodeLengthRepeatCode) {
      repeat = 0;
      code_lengths[symbol++] = code_len;
      if (code_len !== 0) {
        prev_code_len = code_len;
        space -= 32768 >> code_len;
      }
    } else {
      var extra_bits = code_len - 14;
      var old_repeat;
      var repeat_delta;
      var new_len = 0;
      if (code_len === kCodeLengthRepeatCode) {
        new_len = prev_code_len;
      }
      if (repeat_code_len !== new_len) {
        repeat = 0;
        repeat_code_len = new_len;
      }
      old_repeat = repeat;
      if (repeat > 0) {
        repeat -= 2;
        repeat <<= extra_bits;
      }
      repeat += br.readBits(extra_bits) + 3;
      repeat_delta = repeat - old_repeat;
      if (symbol + repeat_delta > num_symbols) {
        throw new Error('[ReadHuffmanCodeLengths] symbol + repeat_delta > num_symbols');
      }
      
      for (var x = 0; x < repeat_delta; x++)
        code_lengths[symbol + x] = repeat_code_len;
      
      symbol += repeat_delta;
      
      if (repeat_code_len !== 0) {
        space -= repeat_delta << (15 - repeat_code_len);
      }
    }
  }
  if (space !== 0) {
    throw new Error("[ReadHuffmanCodeLengths] space = " + space);
  }
  
  for (; symbol < num_symbols; symbol++)
    code_lengths[symbol] = 0;
}

function ReadHuffmanCode(alphabet_size, tables, table, br) {
  var table_size = 0;
  var simple_code_or_skip;
  var code_lengths = new Uint8Array(alphabet_size);
  
  br.readMoreInput();
  
  /* simple_code_or_skip is used as follows:
     1 for simple code;
     0 for no skipping, 2 skips 2 code lengths, 3 skips 3 code lengths */
  simple_code_or_skip = br.readBits(2);
  if (simple_code_or_skip === 1) {
    /* Read symbols, codes & code lengths directly. */
    var i;
    var max_bits_counter = alphabet_size - 1;
    var max_bits = 0;
    var symbols = new Int32Array(4);
    var num_symbols = br.readBits(2) + 1;
    while (max_bits_counter) {
      max_bits_counter >>= 1;
      ++max_bits;
    }

    for (i = 0; i < num_symbols; ++i) {
      symbols[i] = br.readBits(max_bits) % alphabet_size;
      code_lengths[symbols[i]] = 2;
    }
    code_lengths[symbols[0]] = 1;
    switch (num_symbols) {
      case 1:
        break;
      case 3:
        if ((symbols[0] === symbols[1]) ||
            (symbols[0] === symbols[2]) ||
            (symbols[1] === symbols[2])) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        break;
      case 2:
        if (symbols[0] === symbols[1]) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        
        code_lengths[symbols[1]] = 1;
        break;
      case 4:
        if ((symbols[0] === symbols[1]) ||
            (symbols[0] === symbols[2]) ||
            (symbols[0] === symbols[3]) ||
            (symbols[1] === symbols[2]) ||
            (symbols[1] === symbols[3]) ||
            (symbols[2] === symbols[3])) {
          throw new Error('[ReadHuffmanCode] invalid symbols');
        }
        
        if (br.readBits(1)) {
          code_lengths[symbols[2]] = 3;
          code_lengths[symbols[3]] = 3;
        } else {
          code_lengths[symbols[0]] = 2;
        }
        break;
    }
  } else {  /* Decode Huffman-coded code lengths. */
    var i;
    var code_length_code_lengths = new Uint8Array(CODE_LENGTH_CODES);
    var space = 32;
    var num_codes = 0;
    /* Static Huffman code for the code length code lengths */
    var huff = [
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(3, 2), 
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(4, 1),
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(3, 2), 
      new HuffmanCode(2, 0), new HuffmanCode(2, 4), new HuffmanCode(2, 3), new HuffmanCode(4, 5)
    ];
    for (i = simple_code_or_skip; i < CODE_LENGTH_CODES && space > 0; ++i) {
      var code_len_idx = kCodeLengthCodeOrder[i];
      var p = 0;
      var v;
      br.fillBitWindow();
      p += (br.val_ >>> br.bit_pos_) & 15;
      br.bit_pos_ += huff[p].bits;
      v = huff[p].value;
      code_length_code_lengths[code_len_idx] = v;
      if (v !== 0) {
        space -= (32 >> v);
        ++num_codes;
      }
    }
    
    if (!(num_codes === 1 || space === 0))
      throw new Error('[ReadHuffmanCode] invalid num_codes or space');
    
    ReadHuffmanCodeLengths(code_length_code_lengths, alphabet_size, code_lengths, br);
  }
  
  table_size = BrotliBuildHuffmanTable(tables, table, HUFFMAN_TABLE_BITS, code_lengths, alphabet_size);
  
  if (table_size === 0) {
    throw new Error("[ReadHuffmanCode] BuildHuffmanTable failed: ");
  }
  
  return table_size;
}

function ReadBlockLength(table, index, br) {
  var code;
  var nbits;
  code = ReadSymbol(table, index, br);
  nbits = Prefix.kBlockLengthPrefixCode[code].nbits;
  return Prefix.kBlockLengthPrefixCode[code].offset + br.readBits(nbits);
}

function TranslateShortCodes(code, ringbuffer, index) {
  var val;
  if (code < NUM_DISTANCE_SHORT_CODES) {
    index += kDistanceShortCodeIndexOffset[code];
    index &= 3;
    val = ringbuffer[index] + kDistanceShortCodeValueOffset[code];
  } else {
    val = code - NUM_DISTANCE_SHORT_CODES + 1;
  }
  return val;
}

function MoveToFront(v, index) {
  var value = v[index];
  var i = index;
  for (; i; --i) v[i] = v[i - 1];
  v[0] = value;
}

function InverseMoveToFrontTransform(v, v_len) {
  var mtf = new Uint8Array(256);
  var i;
  for (i = 0; i < 256; ++i) {
    mtf[i] = i;
  }
  for (i = 0; i < v_len; ++i) {
    var index = v[i];
    v[i] = mtf[index];
    if (index) MoveToFront(mtf, index);
  }
}

/* Contains a collection of huffman trees with the same alphabet size. */
function HuffmanTreeGroup(alphabet_size, num_htrees) {
  this.alphabet_size = alphabet_size;
  this.num_htrees = num_htrees;
  this.codes = new Array(num_htrees + num_htrees * kMaxHuffmanTableSize[(alphabet_size + 31) >>> 5]);  
  this.htrees = new Uint32Array(num_htrees);
}

HuffmanTreeGroup.prototype.decode = function(br) {
  var i;
  var table_size;
  var next = 0;
  for (i = 0; i < this.num_htrees; ++i) {
    this.htrees[i] = next;
    table_size = ReadHuffmanCode(this.alphabet_size, this.codes, next, br);
    next += table_size;
  }
};

function DecodeContextMap(context_map_size, br) {
  var out = { num_htrees: null, context_map: null };
  var use_rle_for_zeros;
  var max_run_length_prefix = 0;
  var table;
  var i;
  
  br.readMoreInput();
  var num_htrees = out.num_htrees = DecodeVarLenUint8(br) + 1;

  var context_map = out.context_map = new Uint8Array(context_map_size);
  if (num_htrees <= 1) {
    return out;
  }

  use_rle_for_zeros = br.readBits(1);
  if (use_rle_for_zeros) {
    max_run_length_prefix = br.readBits(4) + 1;
  }
  
  table = [];
  for (i = 0; i < HUFFMAN_MAX_TABLE_SIZE; i++) {
    table[i] = new HuffmanCode(0, 0);
  }
  
  ReadHuffmanCode(num_htrees + max_run_length_prefix, table, 0, br);
  
  for (i = 0; i < context_map_size;) {
    var code;

    br.readMoreInput();
    code = ReadSymbol(table, 0, br);
    if (code === 0) {
      context_map[i] = 0;
      ++i;
    } else if (code <= max_run_length_prefix) {
      var reps = 1 + (1 << code) + br.readBits(code);
      while (--reps) {
        if (i >= context_map_size) {
          throw new Error("[DecodeContextMap] i >= context_map_size");
        }
        context_map[i] = 0;
        ++i;
      }
    } else {
      context_map[i] = code - max_run_length_prefix;
      ++i;
    }
  }
  if (br.readBits(1)) {
    InverseMoveToFrontTransform(context_map, context_map_size);
  }
  
  return out;
}

function DecodeBlockType(max_block_type, trees, tree_type, block_types, ringbuffers, indexes, br) {
  var ringbuffer = tree_type * 2;
  var index = tree_type;
  var type_code = ReadSymbol(trees, tree_type * HUFFMAN_MAX_TABLE_SIZE, br);
  var block_type;
  if (type_code === 0) {
    block_type = ringbuffers[ringbuffer + (indexes[index] & 1)];
  } else if (type_code === 1) {
    block_type = ringbuffers[ringbuffer + ((indexes[index] - 1) & 1)] + 1;
  } else {
    block_type = type_code - 2;
  }
  if (block_type >= max_block_type) {
    block_type -= max_block_type;
  }
  block_types[tree_type] = block_type;
  ringbuffers[ringbuffer + (indexes[index] & 1)] = block_type;
  ++indexes[index];
}

function CopyUncompressedBlockToOutput(output, len, pos, ringbuffer, ringbuffer_mask, br) {
  var rb_size = ringbuffer_mask + 1;
  var rb_pos = pos & ringbuffer_mask;
  var br_pos = br.pos_ & BrotliBitReader.IBUF_MASK;
  var nbytes;

  /* For short lengths copy byte-by-byte */
  if (len < 8 || br.bit_pos_ + (len << 3) < br.bit_end_pos_) {
    while (len-- > 0) {
      br.readMoreInput();
      ringbuffer[rb_pos++] = br.readBits(8);
      if (rb_pos === rb_size) {
        output.write(ringbuffer, rb_size);
        rb_pos = 0;
      }
    }
    return;
  }

  if (br.bit_end_pos_ < 32) {
    throw new Error('[CopyUncompressedBlockToOutput] br.bit_end_pos_ < 32');
  }

  /* Copy remaining 0-4 bytes from br.val_ to ringbuffer. */
  while (br.bit_pos_ < 32) {
    ringbuffer[rb_pos] = (br.val_ >>> br.bit_pos_);
    br.bit_pos_ += 8;
    ++rb_pos;
    --len;
  }

  /* Copy remaining bytes from br.buf_ to ringbuffer. */
  nbytes = (br.bit_end_pos_ - br.bit_pos_) >> 3;
  if (br_pos + nbytes > BrotliBitReader.IBUF_MASK) {
    var tail = BrotliBitReader.IBUF_MASK + 1 - br_pos;
    for (var x = 0; x < tail; x++)
      ringbuffer[rb_pos + x] = br.buf_[br_pos + x];
    
    nbytes -= tail;
    rb_pos += tail;
    len -= tail;
    br_pos = 0;
  }

  for (var x = 0; x < nbytes; x++)
    ringbuffer[rb_pos + x] = br.buf_[br_pos + x];
  
  rb_pos += nbytes;
  len -= nbytes;

  /* If we wrote past the logical end of the ringbuffer, copy the tail of the
     ringbuffer to its beginning and flush the ringbuffer to the output. */
  if (rb_pos >= rb_size) {
    output.write(ringbuffer, rb_size);
    rb_pos -= rb_size;    
    for (var x = 0; x < rb_pos; x++)
      ringbuffer[x] = ringbuffer[rb_size + x];
  }

  /* If we have more to copy than the remaining size of the ringbuffer, then we
     first fill the ringbuffer from the input and then flush the ringbuffer to
     the output */
  while (rb_pos + len >= rb_size) {
    nbytes = rb_size - rb_pos;
    if (br.input_.read(ringbuffer, rb_pos, nbytes) < nbytes) {
      throw new Error('[CopyUncompressedBlockToOutput] not enough bytes');
    }
    output.write(ringbuffer, rb_size);
    len -= nbytes;
    rb_pos = 0;
  }

  /* Copy straight from the input onto the ringbuffer. The ringbuffer will be
     flushed to the output at a later time. */
  if (br.input_.read(ringbuffer, rb_pos, len) < len) {
    throw new Error('[CopyUncompressedBlockToOutput] not enough bytes');
  }

  /* Restore the state of the bit reader. */
  br.reset();
}

/* Advances the bit reader position to the next byte boundary and verifies
   that any skipped bits are set to zero. */
function JumpToByteBoundary(br) {
  var new_bit_pos = (br.bit_pos_ + 7) & ~7;
  var pad_bits = br.readBits(new_bit_pos - br.bit_pos_);
  return pad_bits == 0;
}

function BrotliDecompressedSize(buffer) {
  var input = new BrotliInput(buffer);
  var br = new BrotliBitReader(input);
  DecodeWindowBits(br);
  var out = DecodeMetaBlockLength(br);
  return out.meta_block_length;
}

__webpack_unused_export__ = BrotliDecompressedSize;

function BrotliDecompressBuffer(buffer, output_size) {
  var input = new BrotliInput(buffer);
  
  if (output_size == null) {
    output_size = BrotliDecompressedSize(buffer);
  }
  
  var output_buffer = new Uint8Array(output_size);
  var output = new BrotliOutput(output_buffer);
  
  BrotliDecompress(input, output);
  
  if (output.pos < output.buffer.length) {
    output.buffer = output.buffer.subarray(0, output.pos);
  }
  
  return output.buffer;
}

exports.BrotliDecompressBuffer = BrotliDecompressBuffer;

function BrotliDecompress(input, output) {
  var i;
  var pos = 0;
  var input_end = 0;
  var window_bits = 0;
  var max_backward_distance;
  var max_distance = 0;
  var ringbuffer_size;
  var ringbuffer_mask;
  var ringbuffer;
  var ringbuffer_end;
  /* This ring buffer holds a few past copy distances that will be used by */
  /* some special distance codes. */
  var dist_rb = [ 16, 15, 11, 4 ];
  var dist_rb_idx = 0;
  /* The previous 2 bytes used for context. */
  var prev_byte1 = 0;
  var prev_byte2 = 0;
  var hgroup = [new HuffmanTreeGroup(0, 0), new HuffmanTreeGroup(0, 0), new HuffmanTreeGroup(0, 0)];
  var block_type_trees;
  var block_len_trees;
  var br;

  /* We need the slack region for the following reasons:
       - always doing two 8-byte copies for fast backward copying
       - transforms
       - flushing the input ringbuffer when decoding uncompressed blocks */
  var kRingBufferWriteAheadSlack = 128 + BrotliBitReader.READ_SIZE;

  br = new BrotliBitReader(input);

  /* Decode window size. */
  window_bits = DecodeWindowBits(br);
  max_backward_distance = (1 << window_bits) - 16;

  ringbuffer_size = 1 << window_bits;
  ringbuffer_mask = ringbuffer_size - 1;
  ringbuffer = new Uint8Array(ringbuffer_size + kRingBufferWriteAheadSlack + BrotliDictionary.maxDictionaryWordLength);
  ringbuffer_end = ringbuffer_size;

  block_type_trees = [];
  block_len_trees = [];
  for (var x = 0; x < 3 * HUFFMAN_MAX_TABLE_SIZE; x++) {
    block_type_trees[x] = new HuffmanCode(0, 0);
    block_len_trees[x] = new HuffmanCode(0, 0);
  }

  while (!input_end) {
    var meta_block_remaining_len = 0;
    var is_uncompressed;
    var block_length = [ 1 << 28, 1 << 28, 1 << 28 ];
    var block_type = [ 0 ];
    var num_block_types = [ 1, 1, 1 ];
    var block_type_rb = [ 0, 1, 0, 1, 0, 1 ];
    var block_type_rb_index = [ 0 ];
    var distance_postfix_bits;
    var num_direct_distance_codes;
    var distance_postfix_mask;
    var num_distance_codes;
    var context_map = null;
    var context_modes = null;
    var num_literal_htrees;
    var dist_context_map = null;
    var num_dist_htrees;
    var context_offset = 0;
    var context_map_slice = null;
    var literal_htree_index = 0;
    var dist_context_offset = 0;
    var dist_context_map_slice = null;
    var dist_htree_index = 0;
    var context_lookup_offset1 = 0;
    var context_lookup_offset2 = 0;
    var context_mode;
    var htree_command;

    for (i = 0; i < 3; ++i) {
      hgroup[i].codes = null;
      hgroup[i].htrees = null;
    }

    br.readMoreInput();
    
    var _out = DecodeMetaBlockLength(br);
    meta_block_remaining_len = _out.meta_block_length;
    if (pos + meta_block_remaining_len > output.buffer.length) {
      /* We need to grow the output buffer to fit the additional data. */
      var tmp = new Uint8Array( pos + meta_block_remaining_len );
      tmp.set( output.buffer );
      output.buffer = tmp;
    }    
    input_end = _out.input_end;
    is_uncompressed = _out.is_uncompressed;
    
    if (_out.is_metadata) {
      JumpToByteBoundary(br);
      
      for (; meta_block_remaining_len > 0; --meta_block_remaining_len) {
        br.readMoreInput();
        /* Read one byte and ignore it. */
        br.readBits(8);
      }
      
      continue;
    }
    
    if (meta_block_remaining_len === 0) {
      continue;
    }
    
    if (is_uncompressed) {
      br.bit_pos_ = (br.bit_pos_ + 7) & ~7;
      CopyUncompressedBlockToOutput(output, meta_block_remaining_len, pos,
                                    ringbuffer, ringbuffer_mask, br);
      pos += meta_block_remaining_len;
      continue;
    }
    
    for (i = 0; i < 3; ++i) {
      num_block_types[i] = DecodeVarLenUint8(br) + 1;
      if (num_block_types[i] >= 2) {
        ReadHuffmanCode(num_block_types[i] + 2, block_type_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        ReadHuffmanCode(kNumBlockLengthCodes, block_len_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        block_length[i] = ReadBlockLength(block_len_trees, i * HUFFMAN_MAX_TABLE_SIZE, br);
        block_type_rb_index[i] = 1;
      }
    }
    
    br.readMoreInput();
    
    distance_postfix_bits = br.readBits(2);
    num_direct_distance_codes = NUM_DISTANCE_SHORT_CODES + (br.readBits(4) << distance_postfix_bits);
    distance_postfix_mask = (1 << distance_postfix_bits) - 1;
    num_distance_codes = (num_direct_distance_codes + (48 << distance_postfix_bits));
    context_modes = new Uint8Array(num_block_types[0]);

    for (i = 0; i < num_block_types[0]; ++i) {
       br.readMoreInput();
       context_modes[i] = (br.readBits(2) << 1);
    }
    
    var _o1 = DecodeContextMap(num_block_types[0] << kLiteralContextBits, br);
    num_literal_htrees = _o1.num_htrees;
    context_map = _o1.context_map;
    
    var _o2 = DecodeContextMap(num_block_types[2] << kDistanceContextBits, br);
    num_dist_htrees = _o2.num_htrees;
    dist_context_map = _o2.context_map;
    
    hgroup[0] = new HuffmanTreeGroup(kNumLiteralCodes, num_literal_htrees);
    hgroup[1] = new HuffmanTreeGroup(kNumInsertAndCopyCodes, num_block_types[1]);
    hgroup[2] = new HuffmanTreeGroup(num_distance_codes, num_dist_htrees);

    for (i = 0; i < 3; ++i) {
      hgroup[i].decode(br);
    }

    context_map_slice = 0;
    dist_context_map_slice = 0;
    context_mode = context_modes[block_type[0]];
    context_lookup_offset1 = Context.lookupOffsets[context_mode];
    context_lookup_offset2 = Context.lookupOffsets[context_mode + 1];
    htree_command = hgroup[1].htrees[0];

    while (meta_block_remaining_len > 0) {
      var cmd_code;
      var range_idx;
      var insert_code;
      var copy_code;
      var insert_length;
      var copy_length;
      var distance_code;
      var distance;
      var context;
      var j;
      var copy_dst;

      br.readMoreInput();
      
      if (block_length[1] === 0) {
        DecodeBlockType(num_block_types[1],
                        block_type_trees, 1, block_type, block_type_rb,
                        block_type_rb_index, br);
        block_length[1] = ReadBlockLength(block_len_trees, HUFFMAN_MAX_TABLE_SIZE, br);
        htree_command = hgroup[1].htrees[block_type[1]];
      }
      --block_length[1];
      cmd_code = ReadSymbol(hgroup[1].codes, htree_command, br);
      range_idx = cmd_code >> 6;
      if (range_idx >= 2) {
        range_idx -= 2;
        distance_code = -1;
      } else {
        distance_code = 0;
      }
      insert_code = Prefix.kInsertRangeLut[range_idx] + ((cmd_code >> 3) & 7);
      copy_code = Prefix.kCopyRangeLut[range_idx] + (cmd_code & 7);
      insert_length = Prefix.kInsertLengthPrefixCode[insert_code].offset +
          br.readBits(Prefix.kInsertLengthPrefixCode[insert_code].nbits);
      copy_length = Prefix.kCopyLengthPrefixCode[copy_code].offset +
          br.readBits(Prefix.kCopyLengthPrefixCode[copy_code].nbits);
      prev_byte1 = ringbuffer[pos-1 & ringbuffer_mask];
      prev_byte2 = ringbuffer[pos-2 & ringbuffer_mask];
      for (j = 0; j < insert_length; ++j) {
        br.readMoreInput();

        if (block_length[0] === 0) {
          DecodeBlockType(num_block_types[0],
                          block_type_trees, 0, block_type, block_type_rb,
                          block_type_rb_index, br);
          block_length[0] = ReadBlockLength(block_len_trees, 0, br);
          context_offset = block_type[0] << kLiteralContextBits;
          context_map_slice = context_offset;
          context_mode = context_modes[block_type[0]];
          context_lookup_offset1 = Context.lookupOffsets[context_mode];
          context_lookup_offset2 = Context.lookupOffsets[context_mode + 1];
        }
        context = (Context.lookup[context_lookup_offset1 + prev_byte1] |
                   Context.lookup[context_lookup_offset2 + prev_byte2]);
        literal_htree_index = context_map[context_map_slice + context];
        --block_length[0];
        prev_byte2 = prev_byte1;
        prev_byte1 = ReadSymbol(hgroup[0].codes, hgroup[0].htrees[literal_htree_index], br);
        ringbuffer[pos & ringbuffer_mask] = prev_byte1;
        if ((pos & ringbuffer_mask) === ringbuffer_mask) {
          output.write(ringbuffer, ringbuffer_size);
        }
        ++pos;
      }
      meta_block_remaining_len -= insert_length;
      if (meta_block_remaining_len <= 0) break;

      if (distance_code < 0) {
        var context;
        
        br.readMoreInput();
        if (block_length[2] === 0) {
          DecodeBlockType(num_block_types[2],
                          block_type_trees, 2, block_type, block_type_rb,
                          block_type_rb_index, br);
          block_length[2] = ReadBlockLength(block_len_trees, 2 * HUFFMAN_MAX_TABLE_SIZE, br);
          dist_context_offset = block_type[2] << kDistanceContextBits;
          dist_context_map_slice = dist_context_offset;
        }
        --block_length[2];
        context = (copy_length > 4 ? 3 : copy_length - 2) & 0xff;
        dist_htree_index = dist_context_map[dist_context_map_slice + context];
        distance_code = ReadSymbol(hgroup[2].codes, hgroup[2].htrees[dist_htree_index], br);
        if (distance_code >= num_direct_distance_codes) {
          var nbits;
          var postfix;
          var offset;
          distance_code -= num_direct_distance_codes;
          postfix = distance_code & distance_postfix_mask;
          distance_code >>= distance_postfix_bits;
          nbits = (distance_code >> 1) + 1;
          offset = ((2 + (distance_code & 1)) << nbits) - 4;
          distance_code = num_direct_distance_codes +
              ((offset + br.readBits(nbits)) <<
               distance_postfix_bits) + postfix;
        }
      }

      /* Convert the distance code to the actual distance by possibly looking */
      /* up past distnaces from the ringbuffer. */
      distance = TranslateShortCodes(distance_code, dist_rb, dist_rb_idx);
      if (distance < 0) {
        throw new Error('[BrotliDecompress] invalid distance');
      }

      if (pos < max_backward_distance &&
          max_distance !== max_backward_distance) {
        max_distance = pos;
      } else {
        max_distance = max_backward_distance;
      }

      copy_dst = pos & ringbuffer_mask;

      if (distance > max_distance) {
        if (copy_length >= BrotliDictionary.minDictionaryWordLength &&
            copy_length <= BrotliDictionary.maxDictionaryWordLength) {
          var offset = BrotliDictionary.offsetsByLength[copy_length];
          var word_id = distance - max_distance - 1;
          var shift = BrotliDictionary.sizeBitsByLength[copy_length];
          var mask = (1 << shift) - 1;
          var word_idx = word_id & mask;
          var transform_idx = word_id >> shift;
          offset += word_idx * copy_length;
          if (transform_idx < Transform.kNumTransforms) {
            var len = Transform.transformDictionaryWord(ringbuffer, copy_dst, offset, copy_length, transform_idx);
            copy_dst += len;
            pos += len;
            meta_block_remaining_len -= len;
            if (copy_dst >= ringbuffer_end) {
              output.write(ringbuffer, ringbuffer_size);
              
              for (var _x = 0; _x < (copy_dst - ringbuffer_end); _x++)
                ringbuffer[_x] = ringbuffer[ringbuffer_end + _x];
            }
          } else {
            throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
              " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
          }
        } else {
          throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
            " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
        }
      } else {
        if (distance_code > 0) {
          dist_rb[dist_rb_idx & 3] = distance;
          ++dist_rb_idx;
        }

        if (copy_length > meta_block_remaining_len) {
          throw new Error("Invalid backward reference. pos: " + pos + " distance: " + distance +
            " len: " + copy_length + " bytes left: " + meta_block_remaining_len);
        }

        for (j = 0; j < copy_length; ++j) {
          ringbuffer[pos & ringbuffer_mask] = ringbuffer[(pos - distance) & ringbuffer_mask];
          if ((pos & ringbuffer_mask) === ringbuffer_mask) {
            output.write(ringbuffer, ringbuffer_size);
          }
          ++pos;
          --meta_block_remaining_len;
        }
      }

      /* When we get here, we must have inserted at least one literal and */
      /* made a copy of at least length two, therefore accessing the last 2 */
      /* bytes is valid. */
      prev_byte1 = ringbuffer[(pos - 1) & ringbuffer_mask];
      prev_byte2 = ringbuffer[(pos - 2) & ringbuffer_mask];
    }

    /* Protect pos from overflow, wrap it around at every GB of input data */
    pos &= 0x3fffffff;
  }

  output.write(ringbuffer, pos & ringbuffer_mask);
}

__webpack_unused_export__ = BrotliDecompress;

BrotliDictionary.init();


/***/ }),

/***/ 84543:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var base64 = __webpack_require__(32504);

/**
 * The normal dictionary-data.js is quite large, which makes it 
 * unsuitable for browser usage. In order to make it smaller, 
 * we read dictionary.bin, which is a compressed version of
 * the dictionary, and on initial load, Brotli decompresses 
 * it's own dictionary. 😜
 */
exports.init = function() {
  var BrotliDecompressBuffer = (__webpack_require__(20980).BrotliDecompressBuffer);
  var compressed = base64.toByteArray(__webpack_require__(13501));
  return BrotliDecompressBuffer(compressed);
};


/***/ }),

/***/ 13501:
/***/ (function(module) {

module.exports="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";


/***/ }),

/***/ 80614:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Collection of static dictionary words.
*/

var data = __webpack_require__(84543);
exports.init = function() {
  exports.dictionary = data.init();
};

exports.offsetsByLength = new Uint32Array([
     0,     0,     0,     0,     0,  4096,  9216, 21504, 35840, 44032,
 53248, 63488, 74752, 87040, 93696, 100864, 104704, 106752, 108928, 113536,
 115968, 118528, 119872, 121280, 122016,
]);

exports.sizeBitsByLength = new Uint8Array([
  0,  0,  0,  0, 10, 10, 11, 11, 10, 10,
 10, 10, 10,  9,  9,  8,  7,  7,  8,  7,
  7,  6,  6,  5,  5,
]);

exports.minDictionaryWordLength = 4;
exports.maxDictionaryWordLength = 24;


/***/ }),

/***/ 81561:
/***/ (function(__unused_webpack_module, exports) {

function HuffmanCode(bits, value) {
  this.bits = bits;   /* number of bits used for this symbol */
  this.value = value; /* symbol value or table offset */
}

exports.z = HuffmanCode;

var MAX_LENGTH = 15;

/* Returns reverse(reverse(key, len) + 1, len), where reverse(key, len) is the
   bit-wise reversal of the len least significant bits of key. */
function GetNextKey(key, len) {
  var step = 1 << (len - 1);
  while (key & step) {
    step >>= 1;
  }
  return (key & (step - 1)) + step;
}

/* Stores code in table[0], table[step], table[2*step], ..., table[end] */
/* Assumes that end is an integer multiple of step */
function ReplicateValue(table, i, step, end, code) {
  do {
    end -= step;
    table[i + end] = new HuffmanCode(code.bits, code.value);
  } while (end > 0);
}

/* Returns the table width of the next 2nd level table. count is the histogram
   of bit lengths for the remaining symbols, len is the code length of the next
   processed symbol */
function NextTableBitSize(count, len, root_bits) {
  var left = 1 << (len - root_bits);
  while (len < MAX_LENGTH) {
    left -= count[len];
    if (left <= 0) break;
    ++len;
    left <<= 1;
  }
  return len - root_bits;
}

exports.u = function(root_table, table, root_bits, code_lengths, code_lengths_size) {
  var start_table = table;
  var code;            /* current table entry */
  var len;             /* current code length */
  var symbol;          /* symbol index in original or sorted table */
  var key;             /* reversed prefix code */
  var step;            /* step size to replicate values in current table */
  var low;             /* low bits for current root entry */
  var mask;            /* mask for low bits */
  var table_bits;      /* key length of current table */
  var table_size;      /* size of current table */
  var total_size;      /* sum of root table size and 2nd level table sizes */
  var sorted;          /* symbols sorted by code length */
  var count = new Int32Array(MAX_LENGTH + 1);  /* number of codes of each length */
  var offset = new Int32Array(MAX_LENGTH + 1);  /* offsets in sorted table for each length */

  sorted = new Int32Array(code_lengths_size);

  /* build histogram of code lengths */
  for (symbol = 0; symbol < code_lengths_size; symbol++) {
    count[code_lengths[symbol]]++;
  }

  /* generate offsets into sorted symbol table by code length */
  offset[1] = 0;
  for (len = 1; len < MAX_LENGTH; len++) {
    offset[len + 1] = offset[len] + count[len];
  }

  /* sort symbols by length, by symbol order within each length */
  for (symbol = 0; symbol < code_lengths_size; symbol++) {
    if (code_lengths[symbol] !== 0) {
      sorted[offset[code_lengths[symbol]]++] = symbol;
    }
  }
  
  table_bits = root_bits;
  table_size = 1 << table_bits;
  total_size = table_size;

  /* special case code with only one value */
  if (offset[MAX_LENGTH] === 1) {
    for (key = 0; key < total_size; ++key) {
      root_table[table + key] = new HuffmanCode(0, sorted[0] & 0xffff);
    }
    
    return total_size;
  }

  /* fill in root table */
  key = 0;
  symbol = 0;
  for (len = 1, step = 2; len <= root_bits; ++len, step <<= 1) {
    for (; count[len] > 0; --count[len]) {
      code = new HuffmanCode(len & 0xff, sorted[symbol++] & 0xffff);
      ReplicateValue(root_table, table + key, step, table_size, code);
      key = GetNextKey(key, len);
    }
  }

  /* fill in 2nd level tables and add pointers to root table */
  mask = total_size - 1;
  low = -1;
  for (len = root_bits + 1, step = 2; len <= MAX_LENGTH; ++len, step <<= 1) {
    for (; count[len] > 0; --count[len]) {
      if ((key & mask) !== low) {
        table += table_size;
        table_bits = NextTableBitSize(count, len, root_bits);
        table_size = 1 << table_bits;
        total_size += table_size;
        low = key & mask;
        root_table[start_table + low] = new HuffmanCode((table_bits + root_bits) & 0xff, ((table - start_table) - low) & 0xffff);
      }
      code = new HuffmanCode((len - root_bits) & 0xff, sorted[symbol++] & 0xffff);
      ReplicateValue(root_table, table + (key >> root_bits), step, table_size, code);
      key = GetNextKey(key, len);
    }
  }
  
  return total_size;
}


/***/ }),

/***/ 42210:
/***/ (function(__unused_webpack_module, exports) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Lookup tables to map prefix codes to value ranges. This is used during
   decoding of the block lengths, literal insertion lengths and copy lengths.
*/

/* Represents the range of values belonging to a prefix code: */
/* [offset, offset + 2^nbits) */
function PrefixCodeRange(offset, nbits) {
  this.offset = offset;
  this.nbits = nbits;
}

exports.kBlockLengthPrefixCode = [
  new PrefixCodeRange(1, 2), new PrefixCodeRange(5, 2), new PrefixCodeRange(9, 2), new PrefixCodeRange(13, 2),
  new PrefixCodeRange(17, 3), new PrefixCodeRange(25, 3), new PrefixCodeRange(33, 3), new PrefixCodeRange(41, 3),
  new PrefixCodeRange(49, 4), new PrefixCodeRange(65, 4), new PrefixCodeRange(81, 4), new PrefixCodeRange(97, 4),
  new PrefixCodeRange(113, 5), new PrefixCodeRange(145, 5), new PrefixCodeRange(177, 5), new PrefixCodeRange(209, 5),
  new PrefixCodeRange(241, 6), new PrefixCodeRange(305, 6), new PrefixCodeRange(369, 7), new PrefixCodeRange(497, 8),
  new PrefixCodeRange(753, 9), new PrefixCodeRange(1265, 10), new PrefixCodeRange(2289, 11), new PrefixCodeRange(4337, 12),
  new PrefixCodeRange(8433, 13), new PrefixCodeRange(16625, 24)
];

exports.kInsertLengthPrefixCode = [
  new PrefixCodeRange(0, 0), new PrefixCodeRange(1, 0), new PrefixCodeRange(2, 0), new PrefixCodeRange(3, 0),
  new PrefixCodeRange(4, 0), new PrefixCodeRange(5, 0), new PrefixCodeRange(6, 1), new PrefixCodeRange(8, 1),
  new PrefixCodeRange(10, 2), new PrefixCodeRange(14, 2), new PrefixCodeRange(18, 3), new PrefixCodeRange(26, 3),
  new PrefixCodeRange(34, 4), new PrefixCodeRange(50, 4), new PrefixCodeRange(66, 5), new PrefixCodeRange(98, 5),
  new PrefixCodeRange(130, 6), new PrefixCodeRange(194, 7), new PrefixCodeRange(322, 8), new PrefixCodeRange(578, 9),
  new PrefixCodeRange(1090, 10), new PrefixCodeRange(2114, 12), new PrefixCodeRange(6210, 14), new PrefixCodeRange(22594, 24),
];

exports.kCopyLengthPrefixCode = [
  new PrefixCodeRange(2, 0), new PrefixCodeRange(3, 0), new PrefixCodeRange(4, 0), new PrefixCodeRange(5, 0),
  new PrefixCodeRange(6, 0), new PrefixCodeRange(7, 0), new PrefixCodeRange(8, 0), new PrefixCodeRange(9, 0),
  new PrefixCodeRange(10, 1), new PrefixCodeRange(12, 1), new PrefixCodeRange(14, 2), new PrefixCodeRange(18, 2),
  new PrefixCodeRange(22, 3), new PrefixCodeRange(30, 3), new PrefixCodeRange(38, 4), new PrefixCodeRange(54, 4),
  new PrefixCodeRange(70, 5), new PrefixCodeRange(102, 5), new PrefixCodeRange(134, 6), new PrefixCodeRange(198, 7),
  new PrefixCodeRange(326, 8), new PrefixCodeRange(582, 9), new PrefixCodeRange(1094, 10), new PrefixCodeRange(2118, 24),
];

exports.kInsertRangeLut = [
  0, 0, 8, 8, 0, 16, 8, 16, 16,
];

exports.kCopyRangeLut = [
  0, 8, 0, 8, 16, 0, 16, 8, 16,
];


/***/ }),

/***/ 98197:
/***/ (function(__unused_webpack_module, exports) {

function BrotliInput(buffer) {
  this.buffer = buffer;
  this.pos = 0;
}

BrotliInput.prototype.read = function(buf, i, count) {
  if (this.pos + count > this.buffer.length) {
    count = this.buffer.length - this.pos;
  }
  
  for (var p = 0; p < count; p++)
    buf[i + p] = this.buffer[this.pos + p];
  
  this.pos += count;
  return count;
}

exports.z = BrotliInput;

function BrotliOutput(buf) {
  this.buffer = buf;
  this.pos = 0;
}

BrotliOutput.prototype.write = function(buf, count) {
  if (this.pos + count > this.buffer.length)
    throw new Error('Output buffer is not large enough');
  
  this.buffer.set(buf.subarray(0, count), this.pos);
  this.pos += count;
  return count;
};

exports.y = BrotliOutput;


/***/ }),

/***/ 87984:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

/* Copyright 2013 Google Inc. All Rights Reserved.

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

   http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.

   Transformations on dictionary words.
*/

var BrotliDictionary = __webpack_require__(80614);

var kIdentity       = 0;
var kOmitLast1      = 1;
var kOmitLast2      = 2;
var kOmitLast3      = 3;
var kOmitLast4      = 4;
var kOmitLast5      = 5;
var kOmitLast6      = 6;
var kOmitLast7      = 7;
var kOmitLast8      = 8;
var kOmitLast9      = 9;
var kUppercaseFirst = 10;
var kUppercaseAll   = 11;
var kOmitFirst1     = 12;
var kOmitFirst2     = 13;
var kOmitFirst3     = 14;
var kOmitFirst4     = 15;
var kOmitFirst5     = 16;
var kOmitFirst6     = 17;
var kOmitFirst7     = 18;
var kOmitFirst8     = 19;
var kOmitFirst9     = 20;

function Transform(prefix, transform, suffix) {
  this.prefix = new Uint8Array(prefix.length);
  this.transform = transform;
  this.suffix = new Uint8Array(suffix.length);
  
  for (var i = 0; i < prefix.length; i++)
    this.prefix[i] = prefix.charCodeAt(i);
  
  for (var i = 0; i < suffix.length; i++)
    this.suffix[i] = suffix.charCodeAt(i);
}

var kTransforms = [
     new Transform(         "", kIdentity,       ""           ),
     new Transform(         "", kIdentity,       " "          ),
     new Transform(        " ", kIdentity,       " "          ),
     new Transform(         "", kOmitFirst1,     ""           ),
     new Transform(         "", kUppercaseFirst, " "          ),
     new Transform(         "", kIdentity,       " the "      ),
     new Transform(        " ", kIdentity,       ""           ),
     new Transform(       "s ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       " of "       ),
     new Transform(         "", kUppercaseFirst, ""           ),
     new Transform(         "", kIdentity,       " and "      ),
     new Transform(         "", kOmitFirst2,     ""           ),
     new Transform(         "", kOmitLast1,      ""           ),
     new Transform(       ", ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       ", "         ),
     new Transform(        " ", kUppercaseFirst, " "          ),
     new Transform(         "", kIdentity,       " in "       ),
     new Transform(         "", kIdentity,       " to "       ),
     new Transform(       "e ", kIdentity,       " "          ),
     new Transform(         "", kIdentity,       "\""         ),
     new Transform(         "", kIdentity,       "."          ),
     new Transform(         "", kIdentity,       "\">"        ),
     new Transform(         "", kIdentity,       "\n"         ),
     new Transform(         "", kOmitLast3,      ""           ),
     new Transform(         "", kIdentity,       "]"          ),
     new Transform(         "", kIdentity,       " for "      ),
     new Transform(         "", kOmitFirst3,     ""           ),
     new Transform(         "", kOmitLast2,      ""           ),
     new Transform(         "", kIdentity,       " a "        ),
     new Transform(         "", kIdentity,       " that "     ),
     new Transform(        " ", kUppercaseFirst, ""           ),
     new Transform(         "", kIdentity,       ". "         ),
     new Transform(        ".", kIdentity,       ""           ),
     new Transform(        " ", kIdentity,       ", "         ),
     new Transform(         "", kOmitFirst4,     ""           ),
     new Transform(         "", kIdentity,       " with "     ),
     new Transform(         "", kIdentity,       "'"          ),
     new Transform(         "", kIdentity,       " from "     ),
     new Transform(         "", kIdentity,       " by "       ),
     new Transform(         "", kOmitFirst5,     ""           ),
     new Transform(         "", kOmitFirst6,     ""           ),
     new Transform(    " the ", kIdentity,       ""           ),
     new Transform(         "", kOmitLast4,      ""           ),
     new Transform(         "", kIdentity,       ". The "     ),
     new Transform(         "", kUppercaseAll,   ""           ),
     new Transform(         "", kIdentity,       " on "       ),
     new Transform(         "", kIdentity,       " as "       ),
     new Transform(         "", kIdentity,       " is "       ),
     new Transform(         "", kOmitLast7,      ""           ),
     new Transform(         "", kOmitLast1,      "ing "       ),
     new Transform(         "", kIdentity,       "\n\t"       ),
     new Transform(         "", kIdentity,       ":"          ),
     new Transform(        " ", kIdentity,       ". "         ),
     new Transform(         "", kIdentity,       "ed "        ),
     new Transform(         "", kOmitFirst9,     ""           ),
     new Transform(         "", kOmitFirst7,     ""           ),
     new Transform(         "", kOmitLast6,      ""           ),
     new Transform(         "", kIdentity,       "("          ),
     new Transform(         "", kUppercaseFirst, ", "         ),
     new Transform(         "", kOmitLast8,      ""           ),
     new Transform(         "", kIdentity,       " at "       ),
     new Transform(         "", kIdentity,       "ly "        ),
     new Transform(    " the ", kIdentity,       " of "       ),
     new Transform(         "", kOmitLast5,      ""           ),
     new Transform(         "", kOmitLast9,      ""           ),
     new Transform(        " ", kUppercaseFirst, ", "         ),
     new Transform(         "", kUppercaseFirst, "\""         ),
     new Transform(        ".", kIdentity,       "("          ),
     new Transform(         "", kUppercaseAll,   " "          ),
     new Transform(         "", kUppercaseFirst, "\">"        ),
     new Transform(         "", kIdentity,       "=\""        ),
     new Transform(        " ", kIdentity,       "."          ),
     new Transform(    ".com/", kIdentity,       ""           ),
     new Transform(    " the ", kIdentity,       " of the "   ),
     new Transform(         "", kUppercaseFirst, "'"          ),
     new Transform(         "", kIdentity,       ". This "    ),
     new Transform(         "", kIdentity,       ","          ),
     new Transform(        ".", kIdentity,       " "          ),
     new Transform(         "", kUppercaseFirst, "("          ),
     new Transform(         "", kUppercaseFirst, "."          ),
     new Transform(         "", kIdentity,       " not "      ),
     new Transform(        " ", kIdentity,       "=\""        ),
     new Transform(         "", kIdentity,       "er "        ),
     new Transform(        " ", kUppercaseAll,   " "          ),
     new Transform(         "", kIdentity,       "al "        ),
     new Transform(        " ", kUppercaseAll,   ""           ),
     new Transform(         "", kIdentity,       "='"         ),
     new Transform(         "", kUppercaseAll,   "\""         ),
     new Transform(         "", kUppercaseFirst, ". "         ),
     new Transform(        " ", kIdentity,       "("          ),
     new Transform(         "", kIdentity,       "ful "       ),
     new Transform(        " ", kUppercaseFirst, ". "         ),
     new Transform(         "", kIdentity,       "ive "       ),
     new Transform(         "", kIdentity,       "less "      ),
     new Transform(         "", kUppercaseAll,   "'"          ),
     new Transform(         "", kIdentity,       "est "       ),
     new Transform(        " ", kUppercaseFirst, "."          ),
     new Transform(         "", kUppercaseAll,   "\">"        ),
     new Transform(        " ", kIdentity,       "='"         ),
     new Transform(         "", kUppercaseFirst, ","          ),
     new Transform(         "", kIdentity,       "ize "       ),
     new Transform(         "", kUppercaseAll,   "."          ),
     new Transform( "\xc2\xa0", kIdentity,       ""           ),
     new Transform(        " ", kIdentity,       ","          ),
     new Transform(         "", kUppercaseFirst, "=\""        ),
     new Transform(         "", kUppercaseAll,   "=\""        ),
     new Transform(         "", kIdentity,       "ous "       ),
     new Transform(         "", kUppercaseAll,   ", "         ),
     new Transform(         "", kUppercaseFirst, "='"         ),
     new Transform(        " ", kUppercaseFirst, ","          ),
     new Transform(        " ", kUppercaseAll,   "=\""        ),
     new Transform(        " ", kUppercaseAll,   ", "         ),
     new Transform(         "", kUppercaseAll,   ","          ),
     new Transform(         "", kUppercaseAll,   "("          ),
     new Transform(         "", kUppercaseAll,   ". "         ),
     new Transform(        " ", kUppercaseAll,   "."          ),
     new Transform(         "", kUppercaseAll,   "='"         ),
     new Transform(        " ", kUppercaseAll,   ". "         ),
     new Transform(        " ", kUppercaseFirst, "=\""        ),
     new Transform(        " ", kUppercaseAll,   "='"         ),
     new Transform(        " ", kUppercaseFirst, "='"         )
];

exports.kTransforms = kTransforms;
exports.kNumTransforms = kTransforms.length;

function ToUpperCase(p, i) {
  if (p[i] < 0xc0) {
    if (p[i] >= 97 && p[i] <= 122) {
      p[i] ^= 32;
    }
    return 1;
  }
  
  /* An overly simplified uppercasing model for utf-8. */
  if (p[i] < 0xe0) {
    p[i + 1] ^= 32;
    return 2;
  }
  
  /* An arbitrary transform for three byte characters. */
  p[i + 2] ^= 5;
  return 3;
}

exports.transformDictionaryWord = function(dst, idx, word, len, transform) {
  var prefix = kTransforms[transform].prefix;
  var suffix = kTransforms[transform].suffix;
  var t = kTransforms[transform].transform;
  var skip = t < kOmitFirst1 ? 0 : t - (kOmitFirst1 - 1);
  var i = 0;
  var start_idx = idx;
  var uppercase;
  
  if (skip > len) {
    skip = len;
  }
  
  var prefix_pos = 0;
  while (prefix_pos < prefix.length) {
    dst[idx++] = prefix[prefix_pos++];
  }
  
  word += skip;
  len -= skip;
  
  if (t <= kOmitLast9) {
    len -= t;
  }
  
  for (i = 0; i < len; i++) {
    dst[idx++] = BrotliDictionary.dictionary[word + i];
  }
  
  uppercase = idx - len;
  
  if (t === kUppercaseFirst) {
    ToUpperCase(dst, uppercase);
  } else if (t === kUppercaseAll) {
    while (len > 0) {
      var step = ToUpperCase(dst, uppercase);
      uppercase += step;
      len -= step;
    }
  }
  
  var suffix_pos = 0;
  while (suffix_pos < suffix.length) {
    dst[idx++] = suffix[suffix_pos++];
  }
  
  return idx - start_idx;
}


/***/ }),

/***/ 34460:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

module.exports = __webpack_require__(20980).BrotliDecompressBuffer;


/***/ }),

/***/ 72908:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];
/* provided dependency */ var process = __webpack_require__(9964);

/* eslint camelcase: "off" */

var assert = __webpack_require__(80182);

var Zstream = __webpack_require__(37468);
var zlib_deflate = __webpack_require__(22925);
var zlib_inflate = __webpack_require__(2269);
var constants = __webpack_require__(81607);

for (var key in constants) {
  exports[key] = constants[key];
}

// zlib modes
exports.NONE = 0;
exports.DEFLATE = 1;
exports.INFLATE = 2;
exports.GZIP = 3;
exports.GUNZIP = 4;
exports.DEFLATERAW = 5;
exports.INFLATERAW = 6;
exports.UNZIP = 7;

var GZIP_HEADER_ID1 = 0x1f;
var GZIP_HEADER_ID2 = 0x8b;

/**
 * Emulate Node's zlib C++ layer for use by the JS layer in index.js
 */
function Zlib(mode) {
  if (typeof mode !== 'number' || mode < exports.DEFLATE || mode > exports.UNZIP) {
    throw new TypeError('Bad argument');
  }

  this.dictionary = null;
  this.err = 0;
  this.flush = 0;
  this.init_done = false;
  this.level = 0;
  this.memLevel = 0;
  this.mode = mode;
  this.strategy = 0;
  this.windowBits = 0;
  this.write_in_progress = false;
  this.pending_close = false;
  this.gzip_id_bytes_read = 0;
}

Zlib.prototype.close = function () {
  if (this.write_in_progress) {
    this.pending_close = true;
    return;
  }

  this.pending_close = false;

  assert(this.init_done, 'close before init');
  assert(this.mode <= exports.UNZIP);

  if (this.mode === exports.DEFLATE || this.mode === exports.GZIP || this.mode === exports.DEFLATERAW) {
    zlib_deflate.deflateEnd(this.strm);
  } else if (this.mode === exports.INFLATE || this.mode === exports.GUNZIP || this.mode === exports.INFLATERAW || this.mode === exports.UNZIP) {
    zlib_inflate.inflateEnd(this.strm);
  }

  this.mode = exports.NONE;

  this.dictionary = null;
};

Zlib.prototype.write = function (flush, input, in_off, in_len, out, out_off, out_len) {
  return this._write(true, flush, input, in_off, in_len, out, out_off, out_len);
};

Zlib.prototype.writeSync = function (flush, input, in_off, in_len, out, out_off, out_len) {
  return this._write(false, flush, input, in_off, in_len, out, out_off, out_len);
};

Zlib.prototype._write = function (async, flush, input, in_off, in_len, out, out_off, out_len) {
  assert.equal(arguments.length, 8);

  assert(this.init_done, 'write before init');
  assert(this.mode !== exports.NONE, 'already finalized');
  assert.equal(false, this.write_in_progress, 'write already in progress');
  assert.equal(false, this.pending_close, 'close is pending');

  this.write_in_progress = true;

  assert.equal(false, flush === undefined, 'must provide flush value');

  this.write_in_progress = true;

  if (flush !== exports.Z_NO_FLUSH && flush !== exports.Z_PARTIAL_FLUSH && flush !== exports.Z_SYNC_FLUSH && flush !== exports.Z_FULL_FLUSH && flush !== exports.Z_FINISH && flush !== exports.Z_BLOCK) {
    throw new Error('Invalid flush value');
  }

  if (input == null) {
    input = Buffer.alloc(0);
    in_len = 0;
    in_off = 0;
  }

  this.strm.avail_in = in_len;
  this.strm.input = input;
  this.strm.next_in = in_off;
  this.strm.avail_out = out_len;
  this.strm.output = out;
  this.strm.next_out = out_off;
  this.flush = flush;

  if (!async) {
    // sync version
    this._process();

    if (this._checkError()) {
      return this._afterSync();
    }
    return;
  }

  // async version
  var self = this;
  process.nextTick(function () {
    self._process();
    self._after();
  });

  return this;
};

Zlib.prototype._afterSync = function () {
  var avail_out = this.strm.avail_out;
  var avail_in = this.strm.avail_in;

  this.write_in_progress = false;

  return [avail_in, avail_out];
};

Zlib.prototype._process = function () {
  var next_expected_header_byte = null;

  // If the avail_out is left at 0, then it means that it ran out
  // of room.  If there was avail_out left over, then it means
  // that all of the input was consumed.
  switch (this.mode) {
    case exports.DEFLATE:
    case exports.GZIP:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflate(this.strm, this.flush);
      break;
    case exports.UNZIP:
      if (this.strm.avail_in > 0) {
        next_expected_header_byte = this.strm.next_in;
      }

      switch (this.gzip_id_bytes_read) {
        case 0:
          if (next_expected_header_byte === null) {
            break;
          }

          if (this.strm.input[next_expected_header_byte] === GZIP_HEADER_ID1) {
            this.gzip_id_bytes_read = 1;
            next_expected_header_byte++;

            if (this.strm.avail_in === 1) {
              // The only available byte was already read.
              break;
            }
          } else {
            this.mode = exports.INFLATE;
            break;
          }

        // fallthrough
        case 1:
          if (next_expected_header_byte === null) {
            break;
          }

          if (this.strm.input[next_expected_header_byte] === GZIP_HEADER_ID2) {
            this.gzip_id_bytes_read = 2;
            this.mode = exports.GUNZIP;
          } else {
            // There is no actual difference between INFLATE and INFLATERAW
            // (after initialization).
            this.mode = exports.INFLATE;
          }

          break;
        default:
          throw new Error('invalid number of gzip magic number bytes read');
      }

    // fallthrough
    case exports.INFLATE:
    case exports.GUNZIP:
    case exports.INFLATERAW:
      this.err = zlib_inflate.inflate(this.strm, this.flush

      // If data was encoded with dictionary
      );if (this.err === exports.Z_NEED_DICT && this.dictionary) {
        // Load it
        this.err = zlib_inflate.inflateSetDictionary(this.strm, this.dictionary);
        if (this.err === exports.Z_OK) {
          // And try to decode again
          this.err = zlib_inflate.inflate(this.strm, this.flush);
        } else if (this.err === exports.Z_DATA_ERROR) {
          // Both inflateSetDictionary() and inflate() return Z_DATA_ERROR.
          // Make it possible for After() to tell a bad dictionary from bad
          // input.
          this.err = exports.Z_NEED_DICT;
        }
      }
      while (this.strm.avail_in > 0 && this.mode === exports.GUNZIP && this.err === exports.Z_STREAM_END && this.strm.next_in[0] !== 0x00) {
        // Bytes remain in input buffer. Perhaps this is another compressed
        // member in the same archive, or just trailing garbage.
        // Trailing zero bytes are okay, though, since they are frequently
        // used for padding.

        this.reset();
        this.err = zlib_inflate.inflate(this.strm, this.flush);
      }
      break;
    default:
      throw new Error('Unknown mode ' + this.mode);
  }
};

Zlib.prototype._checkError = function () {
  // Acceptable error states depend on the type of zlib stream.
  switch (this.err) {
    case exports.Z_OK:
    case exports.Z_BUF_ERROR:
      if (this.strm.avail_out !== 0 && this.flush === exports.Z_FINISH) {
        this._error('unexpected end of file');
        return false;
      }
      break;
    case exports.Z_STREAM_END:
      // normal statuses, not fatal
      break;
    case exports.Z_NEED_DICT:
      if (this.dictionary == null) {
        this._error('Missing dictionary');
      } else {
        this._error('Bad dictionary');
      }
      return false;
    default:
      // something else.
      this._error('Zlib error');
      return false;
  }

  return true;
};

Zlib.prototype._after = function () {
  if (!this._checkError()) {
    return;
  }

  var avail_out = this.strm.avail_out;
  var avail_in = this.strm.avail_in;

  this.write_in_progress = false;

  // call the write() cb
  this.callback(avail_in, avail_out);

  if (this.pending_close) {
    this.close();
  }
};

Zlib.prototype._error = function (message) {
  if (this.strm.msg) {
    message = this.strm.msg;
  }
  this.onerror(message, this.err

  // no hope of rescue.
  );this.write_in_progress = false;
  if (this.pending_close) {
    this.close();
  }
};

Zlib.prototype.init = function (windowBits, level, memLevel, strategy, dictionary) {
  assert(arguments.length === 4 || arguments.length === 5, 'init(windowBits, level, memLevel, strategy, [dictionary])');

  assert(windowBits >= 8 && windowBits <= 15, 'invalid windowBits');
  assert(level >= -1 && level <= 9, 'invalid compression level');

  assert(memLevel >= 1 && memLevel <= 9, 'invalid memlevel');

  assert(strategy === exports.Z_FILTERED || strategy === exports.Z_HUFFMAN_ONLY || strategy === exports.Z_RLE || strategy === exports.Z_FIXED || strategy === exports.Z_DEFAULT_STRATEGY, 'invalid strategy');

  this._init(level, windowBits, memLevel, strategy, dictionary);
  this._setDictionary();
};

Zlib.prototype.params = function () {
  throw new Error('deflateParams Not supported');
};

Zlib.prototype.reset = function () {
  this._reset();
  this._setDictionary();
};

Zlib.prototype._init = function (level, windowBits, memLevel, strategy, dictionary) {
  this.level = level;
  this.windowBits = windowBits;
  this.memLevel = memLevel;
  this.strategy = strategy;

  this.flush = exports.Z_NO_FLUSH;

  this.err = exports.Z_OK;

  if (this.mode === exports.GZIP || this.mode === exports.GUNZIP) {
    this.windowBits += 16;
  }

  if (this.mode === exports.UNZIP) {
    this.windowBits += 32;
  }

  if (this.mode === exports.DEFLATERAW || this.mode === exports.INFLATERAW) {
    this.windowBits = -1 * this.windowBits;
  }

  this.strm = new Zstream();

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.GZIP:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflateInit2(this.strm, this.level, exports.Z_DEFLATED, this.windowBits, this.memLevel, this.strategy);
      break;
    case exports.INFLATE:
    case exports.GUNZIP:
    case exports.INFLATERAW:
    case exports.UNZIP:
      this.err = zlib_inflate.inflateInit2(this.strm, this.windowBits);
      break;
    default:
      throw new Error('Unknown mode ' + this.mode);
  }

  if (this.err !== exports.Z_OK) {
    this._error('Init error');
  }

  this.dictionary = dictionary;

  this.write_in_progress = false;
  this.init_done = true;
};

Zlib.prototype._setDictionary = function () {
  if (this.dictionary == null) {
    return;
  }

  this.err = exports.Z_OK;

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.DEFLATERAW:
      this.err = zlib_deflate.deflateSetDictionary(this.strm, this.dictionary);
      break;
    default:
      break;
  }

  if (this.err !== exports.Z_OK) {
    this._error('Failed to set dictionary');
  }
};

Zlib.prototype._reset = function () {
  this.err = exports.Z_OK;

  switch (this.mode) {
    case exports.DEFLATE:
    case exports.DEFLATERAW:
    case exports.GZIP:
      this.err = zlib_deflate.deflateReset(this.strm);
      break;
    case exports.INFLATE:
    case exports.INFLATERAW:
    case exports.GUNZIP:
      this.err = zlib_inflate.inflateReset(this.strm);
      break;
    default:
      break;
  }

  if (this.err !== exports.Z_OK) {
    this._error('Failed to reset stream');
  }
};

exports.Zlib = Zlib;

/***/ }),

/***/ 6729:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);


var Buffer = (__webpack_require__(14598).Buffer);
var Transform = (__webpack_require__(9760).Transform);
var binding = __webpack_require__(72908);
var util = __webpack_require__(7187);
var assert = (__webpack_require__(80182).ok);
var kMaxLength = (__webpack_require__(14598).kMaxLength);
var kRangeErrorMessage = 'Cannot create final Buffer. It would be larger ' + 'than 0x' + kMaxLength.toString(16) + ' bytes';

// zlib doesn't provide these, so kludge them in following the same
// const naming scheme zlib uses.
binding.Z_MIN_WINDOWBITS = 8;
binding.Z_MAX_WINDOWBITS = 15;
binding.Z_DEFAULT_WINDOWBITS = 15;

// fewer than 64 bytes per chunk is stupid.
// technically it could work with as few as 8, but even 64 bytes
// is absurdly low.  Usually a MB or more is best.
binding.Z_MIN_CHUNK = 64;
binding.Z_MAX_CHUNK = Infinity;
binding.Z_DEFAULT_CHUNK = 16 * 1024;

binding.Z_MIN_MEMLEVEL = 1;
binding.Z_MAX_MEMLEVEL = 9;
binding.Z_DEFAULT_MEMLEVEL = 8;

binding.Z_MIN_LEVEL = -1;
binding.Z_MAX_LEVEL = 9;
binding.Z_DEFAULT_LEVEL = binding.Z_DEFAULT_COMPRESSION;

// expose all the zlib constants
var bkeys = Object.keys(binding);
for (var bk = 0; bk < bkeys.length; bk++) {
  var bkey = bkeys[bk];
  if (bkey.match(/^Z/)) {
    Object.defineProperty(exports, bkey, {
      enumerable: true, value: binding[bkey], writable: false
    });
  }
}

// translation table for return codes.
var codes = {
  Z_OK: binding.Z_OK,
  Z_STREAM_END: binding.Z_STREAM_END,
  Z_NEED_DICT: binding.Z_NEED_DICT,
  Z_ERRNO: binding.Z_ERRNO,
  Z_STREAM_ERROR: binding.Z_STREAM_ERROR,
  Z_DATA_ERROR: binding.Z_DATA_ERROR,
  Z_MEM_ERROR: binding.Z_MEM_ERROR,
  Z_BUF_ERROR: binding.Z_BUF_ERROR,
  Z_VERSION_ERROR: binding.Z_VERSION_ERROR
};

var ckeys = Object.keys(codes);
for (var ck = 0; ck < ckeys.length; ck++) {
  var ckey = ckeys[ck];
  codes[codes[ckey]] = ckey;
}

Object.defineProperty(exports, "codes", ({
  enumerable: true, value: Object.freeze(codes), writable: false
}));

exports.Deflate = Deflate;
exports.Inflate = Inflate;
exports.Gzip = Gzip;
exports.Gunzip = Gunzip;
exports.DeflateRaw = DeflateRaw;
exports.InflateRaw = InflateRaw;
exports.Unzip = Unzip;

exports.createDeflate = function (o) {
  return new Deflate(o);
};

exports.createInflate = function (o) {
  return new Inflate(o);
};

exports.createDeflateRaw = function (o) {
  return new DeflateRaw(o);
};

exports.createInflateRaw = function (o) {
  return new InflateRaw(o);
};

exports.createGzip = function (o) {
  return new Gzip(o);
};

exports.createGunzip = function (o) {
  return new Gunzip(o);
};

exports.createUnzip = function (o) {
  return new Unzip(o);
};

// Convenience methods.
// compress/decompress a string or buffer in one step.
exports.deflate = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Deflate(opts), buffer, callback);
};

exports.deflateSync = function (buffer, opts) {
  return zlibBufferSync(new Deflate(opts), buffer);
};

exports.gzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Gzip(opts), buffer, callback);
};

exports.gzipSync = function (buffer, opts) {
  return zlibBufferSync(new Gzip(opts), buffer);
};

exports.deflateRaw = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new DeflateRaw(opts), buffer, callback);
};

exports.deflateRawSync = function (buffer, opts) {
  return zlibBufferSync(new DeflateRaw(opts), buffer);
};

exports.unzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Unzip(opts), buffer, callback);
};

exports.unzipSync = function (buffer, opts) {
  return zlibBufferSync(new Unzip(opts), buffer);
};

exports.inflate = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Inflate(opts), buffer, callback);
};

exports.inflateSync = function (buffer, opts) {
  return zlibBufferSync(new Inflate(opts), buffer);
};

exports.gunzip = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new Gunzip(opts), buffer, callback);
};

exports.gunzipSync = function (buffer, opts) {
  return zlibBufferSync(new Gunzip(opts), buffer);
};

exports.inflateRaw = function (buffer, opts, callback) {
  if (typeof opts === 'function') {
    callback = opts;
    opts = {};
  }
  return zlibBuffer(new InflateRaw(opts), buffer, callback);
};

exports.inflateRawSync = function (buffer, opts) {
  return zlibBufferSync(new InflateRaw(opts), buffer);
};

function zlibBuffer(engine, buffer, callback) {
  var buffers = [];
  var nread = 0;

  engine.on('error', onError);
  engine.on('end', onEnd);

  engine.end(buffer);
  flow();

  function flow() {
    var chunk;
    while (null !== (chunk = engine.read())) {
      buffers.push(chunk);
      nread += chunk.length;
    }
    engine.once('readable', flow);
  }

  function onError(err) {
    engine.removeListener('end', onEnd);
    engine.removeListener('readable', flow);
    callback(err);
  }

  function onEnd() {
    var buf;
    var err = null;

    if (nread >= kMaxLength) {
      err = new RangeError(kRangeErrorMessage);
    } else {
      buf = Buffer.concat(buffers, nread);
    }

    buffers = [];
    engine.close();
    callback(err, buf);
  }
}

function zlibBufferSync(engine, buffer) {
  if (typeof buffer === 'string') buffer = Buffer.from(buffer);

  if (!Buffer.isBuffer(buffer)) throw new TypeError('Not a string or buffer');

  var flushFlag = engine._finishFlushFlag;

  return engine._processChunk(buffer, flushFlag);
}

// generic zlib
// minimal 2-byte header
function Deflate(opts) {
  if (!(this instanceof Deflate)) return new Deflate(opts);
  Zlib.call(this, opts, binding.DEFLATE);
}

function Inflate(opts) {
  if (!(this instanceof Inflate)) return new Inflate(opts);
  Zlib.call(this, opts, binding.INFLATE);
}

// gzip - bigger header, same deflate compression
function Gzip(opts) {
  if (!(this instanceof Gzip)) return new Gzip(opts);
  Zlib.call(this, opts, binding.GZIP);
}

function Gunzip(opts) {
  if (!(this instanceof Gunzip)) return new Gunzip(opts);
  Zlib.call(this, opts, binding.GUNZIP);
}

// raw - no header
function DeflateRaw(opts) {
  if (!(this instanceof DeflateRaw)) return new DeflateRaw(opts);
  Zlib.call(this, opts, binding.DEFLATERAW);
}

function InflateRaw(opts) {
  if (!(this instanceof InflateRaw)) return new InflateRaw(opts);
  Zlib.call(this, opts, binding.INFLATERAW);
}

// auto-detect header.
function Unzip(opts) {
  if (!(this instanceof Unzip)) return new Unzip(opts);
  Zlib.call(this, opts, binding.UNZIP);
}

function isValidFlushFlag(flag) {
  return flag === binding.Z_NO_FLUSH || flag === binding.Z_PARTIAL_FLUSH || flag === binding.Z_SYNC_FLUSH || flag === binding.Z_FULL_FLUSH || flag === binding.Z_FINISH || flag === binding.Z_BLOCK;
}

// the Zlib class they all inherit from
// This thing manages the queue of requests, and returns
// true or false if there is anything in the queue when
// you call the .write() method.

function Zlib(opts, mode) {
  var _this = this;

  this._opts = opts = opts || {};
  this._chunkSize = opts.chunkSize || exports.Z_DEFAULT_CHUNK;

  Transform.call(this, opts);

  if (opts.flush && !isValidFlushFlag(opts.flush)) {
    throw new Error('Invalid flush flag: ' + opts.flush);
  }
  if (opts.finishFlush && !isValidFlushFlag(opts.finishFlush)) {
    throw new Error('Invalid flush flag: ' + opts.finishFlush);
  }

  this._flushFlag = opts.flush || binding.Z_NO_FLUSH;
  this._finishFlushFlag = typeof opts.finishFlush !== 'undefined' ? opts.finishFlush : binding.Z_FINISH;

  if (opts.chunkSize) {
    if (opts.chunkSize < exports.Z_MIN_CHUNK || opts.chunkSize > exports.Z_MAX_CHUNK) {
      throw new Error('Invalid chunk size: ' + opts.chunkSize);
    }
  }

  if (opts.windowBits) {
    if (opts.windowBits < exports.Z_MIN_WINDOWBITS || opts.windowBits > exports.Z_MAX_WINDOWBITS) {
      throw new Error('Invalid windowBits: ' + opts.windowBits);
    }
  }

  if (opts.level) {
    if (opts.level < exports.Z_MIN_LEVEL || opts.level > exports.Z_MAX_LEVEL) {
      throw new Error('Invalid compression level: ' + opts.level);
    }
  }

  if (opts.memLevel) {
    if (opts.memLevel < exports.Z_MIN_MEMLEVEL || opts.memLevel > exports.Z_MAX_MEMLEVEL) {
      throw new Error('Invalid memLevel: ' + opts.memLevel);
    }
  }

  if (opts.strategy) {
    if (opts.strategy != exports.Z_FILTERED && opts.strategy != exports.Z_HUFFMAN_ONLY && opts.strategy != exports.Z_RLE && opts.strategy != exports.Z_FIXED && opts.strategy != exports.Z_DEFAULT_STRATEGY) {
      throw new Error('Invalid strategy: ' + opts.strategy);
    }
  }

  if (opts.dictionary) {
    if (!Buffer.isBuffer(opts.dictionary)) {
      throw new Error('Invalid dictionary: it should be a Buffer instance');
    }
  }

  this._handle = new binding.Zlib(mode);

  var self = this;
  this._hadError = false;
  this._handle.onerror = function (message, errno) {
    // there is no way to cleanly recover.
    // continuing only obscures problems.
    _close(self);
    self._hadError = true;

    var error = new Error(message);
    error.errno = errno;
    error.code = exports.codes[errno];
    self.emit('error', error);
  };

  var level = exports.Z_DEFAULT_COMPRESSION;
  if (typeof opts.level === 'number') level = opts.level;

  var strategy = exports.Z_DEFAULT_STRATEGY;
  if (typeof opts.strategy === 'number') strategy = opts.strategy;

  this._handle.init(opts.windowBits || exports.Z_DEFAULT_WINDOWBITS, level, opts.memLevel || exports.Z_DEFAULT_MEMLEVEL, strategy, opts.dictionary);

  this._buffer = Buffer.allocUnsafe(this._chunkSize);
  this._offset = 0;
  this._level = level;
  this._strategy = strategy;

  this.once('end', this.close);

  Object.defineProperty(this, '_closed', {
    get: function () {
      return !_this._handle;
    },
    configurable: true,
    enumerable: true
  });
}

util.inherits(Zlib, Transform);

Zlib.prototype.params = function (level, strategy, callback) {
  if (level < exports.Z_MIN_LEVEL || level > exports.Z_MAX_LEVEL) {
    throw new RangeError('Invalid compression level: ' + level);
  }
  if (strategy != exports.Z_FILTERED && strategy != exports.Z_HUFFMAN_ONLY && strategy != exports.Z_RLE && strategy != exports.Z_FIXED && strategy != exports.Z_DEFAULT_STRATEGY) {
    throw new TypeError('Invalid strategy: ' + strategy);
  }

  if (this._level !== level || this._strategy !== strategy) {
    var self = this;
    this.flush(binding.Z_SYNC_FLUSH, function () {
      assert(self._handle, 'zlib binding closed');
      self._handle.params(level, strategy);
      if (!self._hadError) {
        self._level = level;
        self._strategy = strategy;
        if (callback) callback();
      }
    });
  } else {
    process.nextTick(callback);
  }
};

Zlib.prototype.reset = function () {
  assert(this._handle, 'zlib binding closed');
  return this._handle.reset();
};

// This is the _flush function called by the transform class,
// internally, when the last chunk has been written.
Zlib.prototype._flush = function (callback) {
  this._transform(Buffer.alloc(0), '', callback);
};

Zlib.prototype.flush = function (kind, callback) {
  var _this2 = this;

  var ws = this._writableState;

  if (typeof kind === 'function' || kind === undefined && !callback) {
    callback = kind;
    kind = binding.Z_FULL_FLUSH;
  }

  if (ws.ended) {
    if (callback) process.nextTick(callback);
  } else if (ws.ending) {
    if (callback) this.once('end', callback);
  } else if (ws.needDrain) {
    if (callback) {
      this.once('drain', function () {
        return _this2.flush(kind, callback);
      });
    }
  } else {
    this._flushFlag = kind;
    this.write(Buffer.alloc(0), '', callback);
  }
};

Zlib.prototype.close = function (callback) {
  _close(this, callback);
  process.nextTick(emitCloseNT, this);
};

function _close(engine, callback) {
  if (callback) process.nextTick(callback);

  // Caller may invoke .close after a zlib error (which will null _handle).
  if (!engine._handle) return;

  engine._handle.close();
  engine._handle = null;
}

function emitCloseNT(self) {
  self.emit('close');
}

Zlib.prototype._transform = function (chunk, encoding, cb) {
  var flushFlag;
  var ws = this._writableState;
  var ending = ws.ending || ws.ended;
  var last = ending && (!chunk || ws.length === chunk.length);

  if (chunk !== null && !Buffer.isBuffer(chunk)) return cb(new Error('invalid input'));

  if (!this._handle) return cb(new Error('zlib binding closed'));

  // If it's the last chunk, or a final flush, we use the Z_FINISH flush flag
  // (or whatever flag was provided using opts.finishFlush).
  // If it's explicitly flushing at some other time, then we use
  // Z_FULL_FLUSH. Otherwise, use Z_NO_FLUSH for maximum compression
  // goodness.
  if (last) flushFlag = this._finishFlushFlag;else {
    flushFlag = this._flushFlag;
    // once we've flushed the last of the queue, stop flushing and
    // go back to the normal behavior.
    if (chunk.length >= ws.length) {
      this._flushFlag = this._opts.flush || binding.Z_NO_FLUSH;
    }
  }

  this._processChunk(chunk, flushFlag, cb);
};

Zlib.prototype._processChunk = function (chunk, flushFlag, cb) {
  var availInBefore = chunk && chunk.length;
  var availOutBefore = this._chunkSize - this._offset;
  var inOff = 0;

  var self = this;

  var async = typeof cb === 'function';

  if (!async) {
    var buffers = [];
    var nread = 0;

    var error;
    this.on('error', function (er) {
      error = er;
    });

    assert(this._handle, 'zlib binding closed');
    do {
      var res = this._handle.writeSync(flushFlag, chunk, // in
      inOff, // in_off
      availInBefore, // in_len
      this._buffer, // out
      this._offset, //out_off
      availOutBefore); // out_len
    } while (!this._hadError && callback(res[0], res[1]));

    if (this._hadError) {
      throw error;
    }

    if (nread >= kMaxLength) {
      _close(this);
      throw new RangeError(kRangeErrorMessage);
    }

    var buf = Buffer.concat(buffers, nread);
    _close(this);

    return buf;
  }

  assert(this._handle, 'zlib binding closed');
  var req = this._handle.write(flushFlag, chunk, // in
  inOff, // in_off
  availInBefore, // in_len
  this._buffer, // out
  this._offset, //out_off
  availOutBefore); // out_len

  req.buffer = chunk;
  req.callback = callback;

  function callback(availInAfter, availOutAfter) {
    // When the callback is used in an async write, the callback's
    // context is the `req` object that was created. The req object
    // is === this._handle, and that's why it's important to null
    // out the values after they are done being used. `this._handle`
    // can stay in memory longer than the callback and buffer are needed.
    if (this) {
      this.buffer = null;
      this.callback = null;
    }

    if (self._hadError) return;

    var have = availOutBefore - availOutAfter;
    assert(have >= 0, 'have should not go down');

    if (have > 0) {
      var out = self._buffer.slice(self._offset, self._offset + have);
      self._offset += have;
      // serve some output to the consumer.
      if (async) {
        self.push(out);
      } else {
        buffers.push(out);
        nread += out.length;
      }
    }

    // exhausted the output buffer, or used all the input create a new one.
    if (availOutAfter === 0 || self._offset >= self._chunkSize) {
      availOutBefore = self._chunkSize;
      self._offset = 0;
      self._buffer = Buffer.allocUnsafe(self._chunkSize);
    }

    if (availOutAfter === 0) {
      // Not actually done.  Need to reprocess.
      // Also, update the availInBefore to the availInAfter value,
      // so that if we have to hit it a third (fourth, etc.) time,
      // it'll have the correct byte counts.
      inOff += availInBefore - availInAfter;
      availInBefore = availInAfter;

      if (!async) return true;

      var newReq = self._handle.write(flushFlag, chunk, inOff, availInBefore, self._buffer, self._offset, self._chunkSize);
      newReq.callback = callback; // this same function
      newReq.buffer = chunk;
      return;
    }

    if (!async) return false;

    // finished with the chunk.
    cb();
  }
};

util.inherits(Deflate, Zlib);
util.inherits(Inflate, Zlib);
util.inherits(Gzip, Zlib);
util.inherits(Gunzip, Zlib);
util.inherits(DeflateRaw, Zlib);
util.inherits(InflateRaw, Zlib);
util.inherits(Unzip, Zlib);

/***/ }),

/***/ 17802:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var bind = __webpack_require__(5049);

var $apply = __webpack_require__(73036);
var $call = __webpack_require__(10078);
var $reflectApply = __webpack_require__(61909);

/** @type {import('./actualApply')} */
module.exports = $reflectApply || bind.call($call, $apply);


/***/ }),

/***/ 28619:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var bind = __webpack_require__(5049);
var $apply = __webpack_require__(73036);
var actualApply = __webpack_require__(17802);

/** @type {import('./applyBind')} */
module.exports = function applyBind() {
	return actualApply(bind, $apply, arguments);
};


/***/ }),

/***/ 73036:
/***/ (function(module) {

"use strict";


/** @type {import('./functionApply')} */
module.exports = Function.prototype.apply;


/***/ }),

/***/ 10078:
/***/ (function(module) {

"use strict";


/** @type {import('./functionCall')} */
module.exports = Function.prototype.call;


/***/ }),

/***/ 36688:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var bind = __webpack_require__(5049);
var $TypeError = __webpack_require__(96785);

var $call = __webpack_require__(10078);
var $actualApply = __webpack_require__(17802);

/** @type {import('.')} */
module.exports = function callBindBasic(args) {
	if (args.length < 1 || typeof args[0] !== 'function') {
		throw new $TypeError('a function is required');
	}
	return $actualApply(bind, $call, args);
};


/***/ }),

/***/ 61909:
/***/ (function(module) {

"use strict";


/** @type {import('./reflectApply')} */
module.exports = typeof Reflect !== 'undefined' && Reflect && Reflect.apply;


/***/ }),

/***/ 67913:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var GetIntrinsic = __webpack_require__(28651);

var callBind = __webpack_require__(26601);

var $indexOf = callBind(GetIntrinsic('String.prototype.indexOf'));

module.exports = function callBoundIntrinsic(name, allowMissing) {
	var intrinsic = GetIntrinsic(name, !!allowMissing);
	if (typeof intrinsic === 'function' && $indexOf(name, '.prototype.') > -1) {
		return callBind(intrinsic);
	}
	return intrinsic;
};


/***/ }),

/***/ 26601:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var setFunctionLength = __webpack_require__(86255);

var $defineProperty = __webpack_require__(56649);

var callBindBasic = __webpack_require__(36688);
var applyBind = __webpack_require__(28619);

module.exports = function callBind(originalFunction) {
	var func = callBindBasic(arguments);
	var adjustedLength = originalFunction.length - (arguments.length - 1);
	return setFunctionLength(
		func,
		1 + (adjustedLength > 0 ? adjustedLength : 0),
		true
	);
};

if ($defineProperty) {
	$defineProperty(module.exports, 'apply', { value: applyBind });
} else {
	module.exports.apply = applyBind;
}


/***/ }),

/***/ 22774:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var GetIntrinsic = __webpack_require__(28651);

var callBindBasic = __webpack_require__(36688);

/** @type {(thisArg: string, searchString: string, position?: number) => number} */
var $indexOf = callBindBasic([GetIntrinsic('%String.prototype.indexOf%')]);

/** @type {import('.')} */
module.exports = function callBoundIntrinsic(name, allowMissing) {
	// eslint-disable-next-line no-extra-parens
	var intrinsic = /** @type {Parameters<typeof callBindBasic>[0][0]} */ (GetIntrinsic(name, !!allowMissing));
	if (typeof intrinsic === 'function' && $indexOf(name, '.prototype.') > -1) {
		return callBindBasic([intrinsic]);
	}
	return intrinsic;
};


/***/ }),

/***/ 41613:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];
var clone = (function() {
'use strict';

/**
 * Clones (copies) an Object using deep copying.
 *
 * This function supports circular references by default, but if you are certain
 * there are no circular references in your object, you can save some CPU time
 * by calling clone(obj, false).
 *
 * Caution: if `circular` is false and `parent` contains circular references,
 * your program may enter an infinite loop and crash.
 *
 * @param `parent` - the object to be cloned
 * @param `circular` - set to true if the object to be cloned may contain
 *    circular references. (optional - true by default)
 * @param `depth` - set to a number if the object is only to be cloned to
 *    a particular depth. (optional - defaults to Infinity)
 * @param `prototype` - sets the prototype to be used when cloning an object.
 *    (optional - defaults to parent prototype).
*/
function clone(parent, circular, depth, prototype) {
  var filter;
  if (typeof circular === 'object') {
    depth = circular.depth;
    prototype = circular.prototype;
    filter = circular.filter;
    circular = circular.circular
  }
  // maintain two arrays for circular references, where corresponding parents
  // and children have the same index
  var allParents = [];
  var allChildren = [];

  var useBuffer = typeof Buffer != 'undefined';

  if (typeof circular == 'undefined')
    circular = true;

  if (typeof depth == 'undefined')
    depth = Infinity;

  // recurse this function so we don't reset allParents and allChildren
  function _clone(parent, depth) {
    // cloning null always returns null
    if (parent === null)
      return null;

    if (depth == 0)
      return parent;

    var child;
    var proto;
    if (typeof parent != 'object') {
      return parent;
    }

    if (clone.__isArray(parent)) {
      child = [];
    } else if (clone.__isRegExp(parent)) {
      child = new RegExp(parent.source, __getRegExpFlags(parent));
      if (parent.lastIndex) child.lastIndex = parent.lastIndex;
    } else if (clone.__isDate(parent)) {
      child = new Date(parent.getTime());
    } else if (useBuffer && Buffer.isBuffer(parent)) {
      if (Buffer.allocUnsafe) {
        // Node.js >= 4.5.0
        child = Buffer.allocUnsafe(parent.length);
      } else {
        // Older Node.js versions
        child = new Buffer(parent.length);
      }
      parent.copy(child);
      return child;
    } else {
      if (typeof prototype == 'undefined') {
        proto = Object.getPrototypeOf(parent);
        child = Object.create(proto);
      }
      else {
        child = Object.create(prototype);
        proto = prototype;
      }
    }

    if (circular) {
      var index = allParents.indexOf(parent);

      if (index != -1) {
        return allChildren[index];
      }
      allParents.push(parent);
      allChildren.push(child);
    }

    for (var i in parent) {
      var attrs;
      if (proto) {
        attrs = Object.getOwnPropertyDescriptor(proto, i);
      }

      if (attrs && attrs.set == null) {
        continue;
      }
      child[i] = _clone(parent[i], depth - 1);
    }

    return child;
  }

  return _clone(parent, depth);
}

/**
 * Simple flat clone using prototype, accepts only objects, usefull for property
 * override on FLAT configuration object (no nested props).
 *
 * USE WITH CAUTION! This may not behave as you wish if you do not know how this
 * works.
 */
clone.clonePrototype = function clonePrototype(parent) {
  if (parent === null)
    return null;

  var c = function () {};
  c.prototype = parent;
  return new c();
};

// private utility functions

function __objToStr(o) {
  return Object.prototype.toString.call(o);
};
clone.__objToStr = __objToStr;

function __isDate(o) {
  return typeof o === 'object' && __objToStr(o) === '[object Date]';
};
clone.__isDate = __isDate;

function __isArray(o) {
  return typeof o === 'object' && __objToStr(o) === '[object Array]';
};
clone.__isArray = __isArray;

function __isRegExp(o) {
  return typeof o === 'object' && __objToStr(o) === '[object RegExp]';
};
clone.__isRegExp = __isRegExp;

function __getRegExpFlags(re) {
  var flags = '';
  if (re.global) flags += 'g';
  if (re.ignoreCase) flags += 'i';
  if (re.multiline) flags += 'm';
  return flags;
};
clone.__getRegExpFlags = __getRegExpFlags;

return clone;
})();

if ( true && module.exports) {
  module.exports = clone;
}


/***/ }),

/***/ 83043:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

__webpack_require__(59883);
var path = __webpack_require__(11206);

module.exports = path.Object.values;


/***/ }),

/***/ 42075:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

__webpack_require__(94910);
__webpack_require__(81755);
__webpack_require__(14032);
__webpack_require__(68067);
__webpack_require__(77074);
__webpack_require__(44455);
__webpack_require__(80986);
__webpack_require__(58281);
var path = __webpack_require__(11206);

module.exports = path.Promise;


/***/ }),

/***/ 98168:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var parent = __webpack_require__(90780);
__webpack_require__(84151);
// TODO: Remove from `core-js@4`
__webpack_require__(98443);
__webpack_require__(49261);
__webpack_require__(67858);

module.exports = parent;


/***/ }),

/***/ 32631:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);
var tryToString = __webpack_require__(68664);

var TypeError = global.TypeError;

// `Assert: IsCallable(argument) is true`
module.exports = function (argument) {
  if (isCallable(argument)) return argument;
  throw TypeError(tryToString(argument) + ' is not a function');
};


/***/ }),

/***/ 69075:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isConstructor = __webpack_require__(20884);
var tryToString = __webpack_require__(68664);

var TypeError = global.TypeError;

// `Assert: IsConstructor(argument) is true`
module.exports = function (argument) {
  if (isConstructor(argument)) return argument;
  throw TypeError(tryToString(argument) + ' is not a constructor');
};


/***/ }),

/***/ 58659:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);

var String = global.String;
var TypeError = global.TypeError;

module.exports = function (argument) {
  if (typeof argument == 'object' || isCallable(argument)) return argument;
  throw TypeError("Can't set " + String(argument) + ' as a prototype');
};


/***/ }),

/***/ 71156:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);
var create = __webpack_require__(10819);
var definePropertyModule = __webpack_require__(95892);

var UNSCOPABLES = wellKnownSymbol('unscopables');
var ArrayPrototype = Array.prototype;

// Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
if (ArrayPrototype[UNSCOPABLES] == undefined) {
  definePropertyModule.f(ArrayPrototype, UNSCOPABLES, {
    configurable: true,
    value: create(null)
  });
}

// add a key to Array.prototype[@@unscopables]
module.exports = function (key) {
  ArrayPrototype[UNSCOPABLES][key] = true;
};


/***/ }),

/***/ 36352:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var charAt = (__webpack_require__(69510).charAt);

// `AdvanceStringIndex` abstract operation
// https://tc39.es/ecma262/#sec-advancestringindex
module.exports = function (S, index, unicode) {
  return index + (unicode ? charAt(S, index).length : 1);
};


/***/ }),

/***/ 2868:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isPrototypeOf = __webpack_require__(70176);

var TypeError = global.TypeError;

module.exports = function (it, Prototype) {
  if (isPrototypeOf(Prototype, it)) return it;
  throw TypeError('Incorrect invocation');
};


/***/ }),

/***/ 34984:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isObject = __webpack_require__(24517);

var String = global.String;
var TypeError = global.TypeError;

// `Assert: Type(argument) is Object`
module.exports = function (argument) {
  if (isObject(argument)) return argument;
  throw TypeError(String(argument) + ' is not an object');
};


/***/ }),

/***/ 92642:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var toObject = __webpack_require__(43162);
var toAbsoluteIndex = __webpack_require__(74841);
var lengthOfArrayLike = __webpack_require__(45495);

var min = Math.min;

// `Array.prototype.copyWithin` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.copywithin
// eslint-disable-next-line es/no-array-prototype-copywithin -- safe
module.exports = [].copyWithin || function copyWithin(target /* = 0 */, start /* = 0, end = @length */) {
  var O = toObject(this);
  var len = lengthOfArrayLike(O);
  var to = toAbsoluteIndex(target, len);
  var from = toAbsoluteIndex(start, len);
  var end = arguments.length > 2 ? arguments[2] : undefined;
  var count = min((end === undefined ? len : toAbsoluteIndex(end, len)) - from, len - to);
  var inc = 1;
  if (from < to && to < from + count) {
    inc = -1;
    from += count - 1;
    to += count - 1;
  }
  while (count-- > 0) {
    if (from in O) O[to] = O[from];
    else delete O[to];
    to += inc;
    from += inc;
  } return O;
};


/***/ }),

/***/ 72864:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var toObject = __webpack_require__(43162);
var toAbsoluteIndex = __webpack_require__(74841);
var lengthOfArrayLike = __webpack_require__(45495);

// `Array.prototype.fill` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.fill
module.exports = function fill(value /* , start = 0, end = @length */) {
  var O = toObject(this);
  var length = lengthOfArrayLike(O);
  var argumentsLength = arguments.length;
  var index = toAbsoluteIndex(argumentsLength > 1 ? arguments[1] : undefined, length);
  var end = argumentsLength > 2 ? arguments[2] : undefined;
  var endPos = end === undefined ? length : toAbsoluteIndex(end, length);
  while (endPos > index) O[index++] = value;
  return O;
};


/***/ }),

/***/ 82938:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $forEach = (__webpack_require__(91102).forEach);
var arrayMethodIsStrict = __webpack_require__(81007);

var STRICT_METHOD = arrayMethodIsStrict('forEach');

// `Array.prototype.forEach` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.foreach
module.exports = !STRICT_METHOD ? function forEach(callbackfn /* , thisArg */) {
  return $forEach(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
// eslint-disable-next-line es/no-array-prototype-foreach -- safe
} : [].forEach;


/***/ }),

/***/ 34269:
/***/ (function(module) {

module.exports = function (Constructor, list) {
  var index = 0;
  var length = list.length;
  var result = new Constructor(length);
  while (length > index) result[index] = list[index++];
  return result;
};


/***/ }),

/***/ 95717:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var bind = __webpack_require__(25567);
var call = __webpack_require__(2834);
var toObject = __webpack_require__(43162);
var callWithSafeIterationClosing = __webpack_require__(97738);
var isArrayIteratorMethod = __webpack_require__(89564);
var isConstructor = __webpack_require__(20884);
var lengthOfArrayLike = __webpack_require__(45495);
var createProperty = __webpack_require__(38639);
var getIterator = __webpack_require__(15892);
var getIteratorMethod = __webpack_require__(13872);

var Array = global.Array;

// `Array.from` method implementation
// https://tc39.es/ecma262/#sec-array.from
module.exports = function from(arrayLike /* , mapfn = undefined, thisArg = undefined */) {
  var O = toObject(arrayLike);
  var IS_CONSTRUCTOR = isConstructor(this);
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  if (mapping) mapfn = bind(mapfn, argumentsLength > 2 ? arguments[2] : undefined);
  var iteratorMethod = getIteratorMethod(O);
  var index = 0;
  var length, result, step, iterator, next, value;
  // if the target is not iterable or it's an array with the default iterator - use a simple case
  if (iteratorMethod && !(this == Array && isArrayIteratorMethod(iteratorMethod))) {
    iterator = getIterator(O, iteratorMethod);
    next = iterator.next;
    result = IS_CONSTRUCTOR ? new this() : [];
    for (;!(step = call(next, iterator)).done; index++) {
      value = mapping ? callWithSafeIterationClosing(iterator, mapfn, [step.value, index], true) : step.value;
      createProperty(result, index, value);
    }
  } else {
    length = lengthOfArrayLike(O);
    result = IS_CONSTRUCTOR ? new this(length) : Array(length);
    for (;length > index; index++) {
      value = mapping ? mapfn(O[index], index) : O[index];
      createProperty(result, index, value);
    }
  }
  result.length = index;
  return result;
};


/***/ }),

/***/ 12636:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toIndexedObject = __webpack_require__(98086);
var toAbsoluteIndex = __webpack_require__(74841);
var lengthOfArrayLike = __webpack_require__(45495);

// `Array.prototype.{ indexOf, includes }` methods implementation
var createMethod = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject($this);
    var length = lengthOfArrayLike(O);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check
    if (IS_INCLUDES && el != el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare -- NaN check
      if (value != value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};

module.exports = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod(false)
};


/***/ }),

/***/ 91102:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var bind = __webpack_require__(25567);
var uncurryThis = __webpack_require__(38347);
var IndexedObject = __webpack_require__(7514);
var toObject = __webpack_require__(43162);
var lengthOfArrayLike = __webpack_require__(45495);
var arraySpeciesCreate = __webpack_require__(45744);

var push = uncurryThis([].push);

// `Array.prototype.{ forEach, map, filter, some, every, find, findIndex, filterReject }` methods implementation
var createMethod = function (TYPE) {
  var IS_MAP = TYPE == 1;
  var IS_FILTER = TYPE == 2;
  var IS_SOME = TYPE == 3;
  var IS_EVERY = TYPE == 4;
  var IS_FIND_INDEX = TYPE == 6;
  var IS_FILTER_REJECT = TYPE == 7;
  var NO_HOLES = TYPE == 5 || IS_FIND_INDEX;
  return function ($this, callbackfn, that, specificCreate) {
    var O = toObject($this);
    var self = IndexedObject(O);
    var boundFunction = bind(callbackfn, that);
    var length = lengthOfArrayLike(self);
    var index = 0;
    var create = specificCreate || arraySpeciesCreate;
    var target = IS_MAP ? create($this, length) : IS_FILTER || IS_FILTER_REJECT ? create($this, 0) : undefined;
    var value, result;
    for (;length > index; index++) if (NO_HOLES || index in self) {
      value = self[index];
      result = boundFunction(value, index, O);
      if (TYPE) {
        if (IS_MAP) target[index] = result; // map
        else if (result) switch (TYPE) {
          case 3: return true;              // some
          case 5: return value;             // find
          case 6: return index;             // findIndex
          case 2: push(target, value);      // filter
        } else switch (TYPE) {
          case 4: return false;             // every
          case 7: push(target, value);      // filterReject
        }
      }
    }
    return IS_FIND_INDEX ? -1 : IS_SOME || IS_EVERY ? IS_EVERY : target;
  };
};

module.exports = {
  // `Array.prototype.forEach` method
  // https://tc39.es/ecma262/#sec-array.prototype.foreach
  forEach: createMethod(0),
  // `Array.prototype.map` method
  // https://tc39.es/ecma262/#sec-array.prototype.map
  map: createMethod(1),
  // `Array.prototype.filter` method
  // https://tc39.es/ecma262/#sec-array.prototype.filter
  filter: createMethod(2),
  // `Array.prototype.some` method
  // https://tc39.es/ecma262/#sec-array.prototype.some
  some: createMethod(3),
  // `Array.prototype.every` method
  // https://tc39.es/ecma262/#sec-array.prototype.every
  every: createMethod(4),
  // `Array.prototype.find` method
  // https://tc39.es/ecma262/#sec-array.prototype.find
  find: createMethod(5),
  // `Array.prototype.findIndex` method
  // https://tc39.es/ecma262/#sec-array.prototype.findIndex
  findIndex: createMethod(6),
  // `Array.prototype.filterReject` method
  // https://github.com/tc39/proposal-array-filtering
  filterReject: createMethod(7)
};


/***/ }),

/***/ 84320:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

/* eslint-disable es/no-array-prototype-lastindexof -- safe */
var apply = __webpack_require__(58448);
var toIndexedObject = __webpack_require__(98086);
var toIntegerOrInfinity = __webpack_require__(26882);
var lengthOfArrayLike = __webpack_require__(45495);
var arrayMethodIsStrict = __webpack_require__(81007);

var min = Math.min;
var $lastIndexOf = [].lastIndexOf;
var NEGATIVE_ZERO = !!$lastIndexOf && 1 / [1].lastIndexOf(1, -0) < 0;
var STRICT_METHOD = arrayMethodIsStrict('lastIndexOf');
var FORCED = NEGATIVE_ZERO || !STRICT_METHOD;

// `Array.prototype.lastIndexOf` method implementation
// https://tc39.es/ecma262/#sec-array.prototype.lastindexof
module.exports = FORCED ? function lastIndexOf(searchElement /* , fromIndex = @[*-1] */) {
  // convert -0 to +0
  if (NEGATIVE_ZERO) return apply($lastIndexOf, this, arguments) || 0;
  var O = toIndexedObject(this);
  var length = lengthOfArrayLike(O);
  var index = length - 1;
  if (arguments.length > 1) index = min(index, toIntegerOrInfinity(arguments[1]));
  if (index < 0) index = length + index;
  for (;index >= 0; index--) if (index in O && O[index] === searchElement) return index || 0;
  return -1;
} : $lastIndexOf;


/***/ }),

/***/ 56280:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var wellKnownSymbol = __webpack_require__(38688);
var V8_VERSION = __webpack_require__(70091);

var SPECIES = wellKnownSymbol('species');

module.exports = function (METHOD_NAME) {
  // We can't use this feature detection in V8 since it causes
  // deoptimization and serious performance degradation
  // https://github.com/zloirock/core-js/issues/677
  return V8_VERSION >= 51 || !fails(function () {
    var array = [];
    var constructor = array.constructor = {};
    constructor[SPECIES] = function () {
      return { foo: 1 };
    };
    return array[METHOD_NAME](Boolean).foo !== 1;
  });
};


/***/ }),

/***/ 81007:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var fails = __webpack_require__(47044);

module.exports = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails(function () {
    // eslint-disable-next-line no-useless-call,no-throw-literal -- required for testing
    method.call(null, argument || function () { throw 1; }, 1);
  });
};


/***/ }),

/***/ 32843:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var aCallable = __webpack_require__(32631);
var toObject = __webpack_require__(43162);
var IndexedObject = __webpack_require__(7514);
var lengthOfArrayLike = __webpack_require__(45495);

var TypeError = global.TypeError;

// `Array.prototype.{ reduce, reduceRight }` methods implementation
var createMethod = function (IS_RIGHT) {
  return function (that, callbackfn, argumentsLength, memo) {
    aCallable(callbackfn);
    var O = toObject(that);
    var self = IndexedObject(O);
    var length = lengthOfArrayLike(O);
    var index = IS_RIGHT ? length - 1 : 0;
    var i = IS_RIGHT ? -1 : 1;
    if (argumentsLength < 2) while (true) {
      if (index in self) {
        memo = self[index];
        index += i;
        break;
      }
      index += i;
      if (IS_RIGHT ? index < 0 : length <= index) {
        throw TypeError('Reduce of empty array with no initial value');
      }
    }
    for (;IS_RIGHT ? index >= 0 : length > index; index += i) if (index in self) {
      memo = callbackfn(memo, self[index], index, O);
    }
    return memo;
  };
};

module.exports = {
  // `Array.prototype.reduce` method
  // https://tc39.es/ecma262/#sec-array.prototype.reduce
  left: createMethod(false),
  // `Array.prototype.reduceRight` method
  // https://tc39.es/ecma262/#sec-array.prototype.reduceright
  right: createMethod(true)
};


/***/ }),

/***/ 73163:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);

module.exports = uncurryThis([].slice);


/***/ }),

/***/ 43977:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var arraySlice = __webpack_require__(73163);

var floor = Math.floor;

var mergeSort = function (array, comparefn) {
  var length = array.length;
  var middle = floor(length / 2);
  return length < 8 ? insertionSort(array, comparefn) : merge(
    array,
    mergeSort(arraySlice(array, 0, middle), comparefn),
    mergeSort(arraySlice(array, middle), comparefn),
    comparefn
  );
};

var insertionSort = function (array, comparefn) {
  var length = array.length;
  var i = 1;
  var element, j;

  while (i < length) {
    j = i;
    element = array[i];
    while (j && comparefn(array[j - 1], element) > 0) {
      array[j] = array[--j];
    }
    if (j !== i++) array[j] = element;
  } return array;
};

var merge = function (array, left, right, comparefn) {
  var llength = left.length;
  var rlength = right.length;
  var lindex = 0;
  var rindex = 0;

  while (lindex < llength || rindex < rlength) {
    array[lindex + rindex] = (lindex < llength && rindex < rlength)
      ? comparefn(left[lindex], right[rindex]) <= 0 ? left[lindex++] : right[rindex++]
      : lindex < llength ? left[lindex++] : right[rindex++];
  } return array;
};

module.exports = mergeSort;


/***/ }),

/***/ 98578:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isArray = __webpack_require__(59113);
var isConstructor = __webpack_require__(20884);
var isObject = __webpack_require__(24517);
var wellKnownSymbol = __webpack_require__(38688);

var SPECIES = wellKnownSymbol('species');
var Array = global.Array;

// a part of `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate
module.exports = function (originalArray) {
  var C;
  if (isArray(originalArray)) {
    C = originalArray.constructor;
    // cross-realm fallback
    if (isConstructor(C) && (C === Array || isArray(C.prototype))) C = undefined;
    else if (isObject(C)) {
      C = C[SPECIES];
      if (C === null) C = undefined;
    }
  } return C === undefined ? Array : C;
};


/***/ }),

/***/ 45744:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var arraySpeciesConstructor = __webpack_require__(98578);

// `ArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#sec-arrayspeciescreate
module.exports = function (originalArray, length) {
  return new (arraySpeciesConstructor(originalArray))(length === 0 ? 0 : length);
};


/***/ }),

/***/ 97738:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var anObject = __webpack_require__(34984);
var iteratorClose = __webpack_require__(50194);

// call something on iterator step with safe closing on error
module.exports = function (iterator, fn, value, ENTRIES) {
  try {
    return ENTRIES ? fn(anObject(value)[0], value[1]) : fn(value);
  } catch (error) {
    iteratorClose(iterator, 'throw', error);
  }
};


/***/ }),

/***/ 46769:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);

var ITERATOR = wellKnownSymbol('iterator');
var SAFE_CLOSING = false;

try {
  var called = 0;
  var iteratorWithReturn = {
    next: function () {
      return { done: !!called++ };
    },
    'return': function () {
      SAFE_CLOSING = true;
    }
  };
  iteratorWithReturn[ITERATOR] = function () {
    return this;
  };
  // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing
  Array.from(iteratorWithReturn, function () { throw 2; });
} catch (error) { /* empty */ }

module.exports = function (exec, SKIP_CLOSING) {
  if (!SKIP_CLOSING && !SAFE_CLOSING) return false;
  var ITERATION_SUPPORT = false;
  try {
    var object = {};
    object[ITERATOR] = function () {
      return {
        next: function () {
          return { done: ITERATION_SUPPORT = true };
        }
      };
    };
    exec(object);
  } catch (error) { /* empty */ }
  return ITERATION_SUPPORT;
};


/***/ }),

/***/ 93975:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);

var toString = uncurryThis({}.toString);
var stringSlice = uncurryThis(''.slice);

module.exports = function (it) {
  return stringSlice(toString(it), 8, -1);
};


/***/ }),

/***/ 52564:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var TO_STRING_TAG_SUPPORT = __webpack_require__(24663);
var isCallable = __webpack_require__(94578);
var classofRaw = __webpack_require__(93975);
var wellKnownSymbol = __webpack_require__(38688);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var Object = global.Object;

// ES3 wrong here
var CORRECT_ARGUMENTS = classofRaw(function () { return arguments; }()) == 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (error) { /* empty */ }
};

// getting tag from ES6+ `Object.prototype.toString`
module.exports = TO_STRING_TAG_SUPPORT ? classofRaw : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (tag = tryGet(O = Object(it), TO_STRING_TAG)) == 'string' ? tag
    // builtinTag case
    : CORRECT_ARGUMENTS ? classofRaw(O)
    // ES3 arguments fallback
    : (result = classofRaw(O)) == 'Object' && isCallable(O.callee) ? 'Arguments' : result;
};


/***/ }),

/***/ 34074:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var arraySlice = __webpack_require__(73163);

var replace = uncurryThis(''.replace);
var split = uncurryThis(''.split);
var join = uncurryThis([].join);

var TEST = (function (arg) { return String(Error(arg).stack); })('zxcasd');
var V8_OR_CHAKRA_STACK_ENTRY = /\n\s*at [^:]*:[^\n]*/;
var IS_V8_OR_CHAKRA_STACK = V8_OR_CHAKRA_STACK_ENTRY.test(TEST);
var IS_FIREFOX_OR_SAFARI_STACK = /@[^\n]*\n/.test(TEST) && !/zxcasd/.test(TEST);

module.exports = function (stack, dropEntries) {
  if (typeof stack != 'string') return stack;
  if (IS_V8_OR_CHAKRA_STACK) {
    while (dropEntries--) stack = replace(stack, V8_OR_CHAKRA_STACK_ENTRY, '');
  } else if (IS_FIREFOX_OR_SAFARI_STACK) {
    return join(arraySlice(split(stack, '\n'), dropEntries), '\n');
  } return stack;
};


/***/ }),

/***/ 9649:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var defineProperty = (__webpack_require__(95892).f);
var create = __webpack_require__(10819);
var redefineAll = __webpack_require__(15341);
var bind = __webpack_require__(25567);
var anInstance = __webpack_require__(2868);
var iterate = __webpack_require__(80383);
var defineIterator = __webpack_require__(97001);
var setSpecies = __webpack_require__(51334);
var DESCRIPTORS = __webpack_require__(15567);
var fastKey = (__webpack_require__(62148).fastKey);
var InternalStateModule = __webpack_require__(70172);

var setInternalState = InternalStateModule.set;
var internalStateGetterFor = InternalStateModule.getterFor;

module.exports = {
  getConstructor: function (wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER) {
    var Constructor = wrapper(function (that, iterable) {
      anInstance(that, Prototype);
      setInternalState(that, {
        type: CONSTRUCTOR_NAME,
        index: create(null),
        first: undefined,
        last: undefined,
        size: 0
      });
      if (!DESCRIPTORS) that.size = 0;
      if (iterable != undefined) iterate(iterable, that[ADDER], { that: that, AS_ENTRIES: IS_MAP });
    });

    var Prototype = Constructor.prototype;

    var getInternalState = internalStateGetterFor(CONSTRUCTOR_NAME);

    var define = function (that, key, value) {
      var state = getInternalState(that);
      var entry = getEntry(that, key);
      var previous, index;
      // change existing entry
      if (entry) {
        entry.value = value;
      // create new entry
      } else {
        state.last = entry = {
          index: index = fastKey(key, true),
          key: key,
          value: value,
          previous: previous = state.last,
          next: undefined,
          removed: false
        };
        if (!state.first) state.first = entry;
        if (previous) previous.next = entry;
        if (DESCRIPTORS) state.size++;
        else that.size++;
        // add to index
        if (index !== 'F') state.index[index] = entry;
      } return that;
    };

    var getEntry = function (that, key) {
      var state = getInternalState(that);
      // fast case
      var index = fastKey(key);
      var entry;
      if (index !== 'F') return state.index[index];
      // frozen object case
      for (entry = state.first; entry; entry = entry.next) {
        if (entry.key == key) return entry;
      }
    };

    redefineAll(Prototype, {
      // `{ Map, Set }.prototype.clear()` methods
      // https://tc39.es/ecma262/#sec-map.prototype.clear
      // https://tc39.es/ecma262/#sec-set.prototype.clear
      clear: function clear() {
        var that = this;
        var state = getInternalState(that);
        var data = state.index;
        var entry = state.first;
        while (entry) {
          entry.removed = true;
          if (entry.previous) entry.previous = entry.previous.next = undefined;
          delete data[entry.index];
          entry = entry.next;
        }
        state.first = state.last = undefined;
        if (DESCRIPTORS) state.size = 0;
        else that.size = 0;
      },
      // `{ Map, Set }.prototype.delete(key)` methods
      // https://tc39.es/ecma262/#sec-map.prototype.delete
      // https://tc39.es/ecma262/#sec-set.prototype.delete
      'delete': function (key) {
        var that = this;
        var state = getInternalState(that);
        var entry = getEntry(that, key);
        if (entry) {
          var next = entry.next;
          var prev = entry.previous;
          delete state.index[entry.index];
          entry.removed = true;
          if (prev) prev.next = next;
          if (next) next.previous = prev;
          if (state.first == entry) state.first = next;
          if (state.last == entry) state.last = prev;
          if (DESCRIPTORS) state.size--;
          else that.size--;
        } return !!entry;
      },
      // `{ Map, Set }.prototype.forEach(callbackfn, thisArg = undefined)` methods
      // https://tc39.es/ecma262/#sec-map.prototype.foreach
      // https://tc39.es/ecma262/#sec-set.prototype.foreach
      forEach: function forEach(callbackfn /* , that = undefined */) {
        var state = getInternalState(this);
        var boundFunction = bind(callbackfn, arguments.length > 1 ? arguments[1] : undefined);
        var entry;
        while (entry = entry ? entry.next : state.first) {
          boundFunction(entry.value, entry.key, this);
          // revert to the last existing entry
          while (entry && entry.removed) entry = entry.previous;
        }
      },
      // `{ Map, Set}.prototype.has(key)` methods
      // https://tc39.es/ecma262/#sec-map.prototype.has
      // https://tc39.es/ecma262/#sec-set.prototype.has
      has: function has(key) {
        return !!getEntry(this, key);
      }
    });

    redefineAll(Prototype, IS_MAP ? {
      // `Map.prototype.get(key)` method
      // https://tc39.es/ecma262/#sec-map.prototype.get
      get: function get(key) {
        var entry = getEntry(this, key);
        return entry && entry.value;
      },
      // `Map.prototype.set(key, value)` method
      // https://tc39.es/ecma262/#sec-map.prototype.set
      set: function set(key, value) {
        return define(this, key === 0 ? 0 : key, value);
      }
    } : {
      // `Set.prototype.add(value)` method
      // https://tc39.es/ecma262/#sec-set.prototype.add
      add: function add(value) {
        return define(this, value = value === 0 ? 0 : value, value);
      }
    });
    if (DESCRIPTORS) defineProperty(Prototype, 'size', {
      get: function () {
        return getInternalState(this).size;
      }
    });
    return Constructor;
  },
  setStrong: function (Constructor, CONSTRUCTOR_NAME, IS_MAP) {
    var ITERATOR_NAME = CONSTRUCTOR_NAME + ' Iterator';
    var getInternalCollectionState = internalStateGetterFor(CONSTRUCTOR_NAME);
    var getInternalIteratorState = internalStateGetterFor(ITERATOR_NAME);
    // `{ Map, Set }.prototype.{ keys, values, entries, @@iterator }()` methods
    // https://tc39.es/ecma262/#sec-map.prototype.entries
    // https://tc39.es/ecma262/#sec-map.prototype.keys
    // https://tc39.es/ecma262/#sec-map.prototype.values
    // https://tc39.es/ecma262/#sec-map.prototype-@@iterator
    // https://tc39.es/ecma262/#sec-set.prototype.entries
    // https://tc39.es/ecma262/#sec-set.prototype.keys
    // https://tc39.es/ecma262/#sec-set.prototype.values
    // https://tc39.es/ecma262/#sec-set.prototype-@@iterator
    defineIterator(Constructor, CONSTRUCTOR_NAME, function (iterated, kind) {
      setInternalState(this, {
        type: ITERATOR_NAME,
        target: iterated,
        state: getInternalCollectionState(iterated),
        kind: kind,
        last: undefined
      });
    }, function () {
      var state = getInternalIteratorState(this);
      var kind = state.kind;
      var entry = state.last;
      // revert to the last existing entry
      while (entry && entry.removed) entry = entry.previous;
      // get next entry
      if (!state.target || !(state.last = entry = entry ? entry.next : state.state.first)) {
        // or finish the iteration
        state.target = undefined;
        return { value: undefined, done: true };
      }
      // return step by kind
      if (kind == 'keys') return { value: entry.key, done: false };
      if (kind == 'values') return { value: entry.value, done: false };
      return { value: [entry.key, entry.value], done: false };
    }, IS_MAP ? 'entries' : 'values', !IS_MAP, true);

    // `{ Map, Set }.prototype[@@species]` accessors
    // https://tc39.es/ecma262/#sec-get-map-@@species
    // https://tc39.es/ecma262/#sec-get-set-@@species
    setSpecies(CONSTRUCTOR_NAME);
  }
};


/***/ }),

/***/ 36673:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var isForced = __webpack_require__(39599);
var redefine = __webpack_require__(13711);
var InternalMetadataModule = __webpack_require__(62148);
var iterate = __webpack_require__(80383);
var anInstance = __webpack_require__(2868);
var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);
var fails = __webpack_require__(47044);
var checkCorrectnessOfIteration = __webpack_require__(46769);
var setToStringTag = __webpack_require__(15216);
var inheritIfRequired = __webpack_require__(51868);

module.exports = function (CONSTRUCTOR_NAME, wrapper, common) {
  var IS_MAP = CONSTRUCTOR_NAME.indexOf('Map') !== -1;
  var IS_WEAK = CONSTRUCTOR_NAME.indexOf('Weak') !== -1;
  var ADDER = IS_MAP ? 'set' : 'add';
  var NativeConstructor = global[CONSTRUCTOR_NAME];
  var NativePrototype = NativeConstructor && NativeConstructor.prototype;
  var Constructor = NativeConstructor;
  var exported = {};

  var fixMethod = function (KEY) {
    var uncurriedNativeMethod = uncurryThis(NativePrototype[KEY]);
    redefine(NativePrototype, KEY,
      KEY == 'add' ? function add(value) {
        uncurriedNativeMethod(this, value === 0 ? 0 : value);
        return this;
      } : KEY == 'delete' ? function (key) {
        return IS_WEAK && !isObject(key) ? false : uncurriedNativeMethod(this, key === 0 ? 0 : key);
      } : KEY == 'get' ? function get(key) {
        return IS_WEAK && !isObject(key) ? undefined : uncurriedNativeMethod(this, key === 0 ? 0 : key);
      } : KEY == 'has' ? function has(key) {
        return IS_WEAK && !isObject(key) ? false : uncurriedNativeMethod(this, key === 0 ? 0 : key);
      } : function set(key, value) {
        uncurriedNativeMethod(this, key === 0 ? 0 : key, value);
        return this;
      }
    );
  };

  var REPLACE = isForced(
    CONSTRUCTOR_NAME,
    !isCallable(NativeConstructor) || !(IS_WEAK || NativePrototype.forEach && !fails(function () {
      new NativeConstructor().entries().next();
    }))
  );

  if (REPLACE) {
    // create collection constructor
    Constructor = common.getConstructor(wrapper, CONSTRUCTOR_NAME, IS_MAP, ADDER);
    InternalMetadataModule.enable();
  } else if (isForced(CONSTRUCTOR_NAME, true)) {
    var instance = new Constructor();
    // early implementations not supports chaining
    var HASNT_CHAINING = instance[ADDER](IS_WEAK ? {} : -0, 1) != instance;
    // V8 ~ Chromium 40- weak-collections throws on primitives, but should return false
    var THROWS_ON_PRIMITIVES = fails(function () { instance.has(1); });
    // most early implementations doesn't supports iterables, most modern - not close it correctly
    // eslint-disable-next-line no-new -- required for testing
    var ACCEPT_ITERABLES = checkCorrectnessOfIteration(function (iterable) { new NativeConstructor(iterable); });
    // for early implementations -0 and +0 not the same
    var BUGGY_ZERO = !IS_WEAK && fails(function () {
      // V8 ~ Chromium 42- fails only with 5+ elements
      var $instance = new NativeConstructor();
      var index = 5;
      while (index--) $instance[ADDER](index, index);
      return !$instance.has(-0);
    });

    if (!ACCEPT_ITERABLES) {
      Constructor = wrapper(function (dummy, iterable) {
        anInstance(dummy, NativePrototype);
        var that = inheritIfRequired(new NativeConstructor(), dummy, Constructor);
        if (iterable != undefined) iterate(iterable, that[ADDER], { that: that, AS_ENTRIES: IS_MAP });
        return that;
      });
      Constructor.prototype = NativePrototype;
      NativePrototype.constructor = Constructor;
    }

    if (THROWS_ON_PRIMITIVES || BUGGY_ZERO) {
      fixMethod('delete');
      fixMethod('has');
      IS_MAP && fixMethod('get');
    }

    if (BUGGY_ZERO || HASNT_CHAINING) fixMethod(ADDER);

    // weak collections should not contains .clear method
    if (IS_WEAK && NativePrototype.clear) delete NativePrototype.clear;
  }

  exported[CONSTRUCTOR_NAME] = Constructor;
  $({ global: true, forced: Constructor != NativeConstructor }, exported);

  setToStringTag(Constructor, CONSTRUCTOR_NAME);

  if (!IS_WEAK) common.setStrong(Constructor, CONSTRUCTOR_NAME, IS_MAP);

  return Constructor;
};


/***/ }),

/***/ 2675:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var hasOwn = __webpack_require__(20340);
var ownKeys = __webpack_require__(21594);
var getOwnPropertyDescriptorModule = __webpack_require__(72062);
var definePropertyModule = __webpack_require__(95892);

module.exports = function (target, source) {
  var keys = ownKeys(source);
  var defineProperty = definePropertyModule.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!hasOwn(target, key)) defineProperty(target, key, getOwnPropertyDescriptor(source, key));
  }
};


/***/ }),

/***/ 91151:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);

var MATCH = wellKnownSymbol('match');

module.exports = function (METHOD_NAME) {
  var regexp = /./;
  try {
    '/./'[METHOD_NAME](regexp);
  } catch (error1) {
    try {
      regexp[MATCH] = false;
      return '/./'[METHOD_NAME](regexp);
    } catch (error2) { /* empty */ }
  } return false;
};


/***/ }),

/***/ 68494:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);

module.exports = !fails(function () {
  function F() { /* empty */ }
  F.prototype.constructor = null;
  // eslint-disable-next-line es/no-object-getprototypeof -- required for testing
  return Object.getPrototypeOf(new F()) !== F.prototype;
});


/***/ }),

/***/ 91159:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var requireObjectCoercible = __webpack_require__(83943);
var toString = __webpack_require__(25096);

var quot = /"/g;
var replace = uncurryThis(''.replace);

// `CreateHTML` abstract operation
// https://tc39.es/ecma262/#sec-createhtml
module.exports = function (string, tag, attribute, value) {
  var S = toString(requireObjectCoercible(string));
  var p1 = '<' + tag;
  if (attribute !== '') p1 += ' ' + attribute + '="' + replace(toString(value), quot, '&quot;') + '"';
  return p1 + '>' + S + '</' + tag + '>';
};


/***/ }),

/***/ 13945:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var IteratorPrototype = (__webpack_require__(5844).IteratorPrototype);
var create = __webpack_require__(10819);
var createPropertyDescriptor = __webpack_require__(97841);
var setToStringTag = __webpack_require__(15216);
var Iterators = __webpack_require__(15366);

var returnThis = function () { return this; };

module.exports = function (IteratorConstructor, NAME, next) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create(IteratorPrototype, { next: createPropertyDescriptor(1, next) });
  setToStringTag(IteratorConstructor, TO_STRING_TAG, false, true);
  Iterators[TO_STRING_TAG] = returnThis;
  return IteratorConstructor;
};


/***/ }),

/***/ 48914:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var definePropertyModule = __webpack_require__(95892);
var createPropertyDescriptor = __webpack_require__(97841);

module.exports = DESCRIPTORS ? function (object, key, value) {
  return definePropertyModule.f(object, key, createPropertyDescriptor(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};


/***/ }),

/***/ 97841:
/***/ (function(module) {

module.exports = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};


/***/ }),

/***/ 38639:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var toPropertyKey = __webpack_require__(63918);
var definePropertyModule = __webpack_require__(95892);
var createPropertyDescriptor = __webpack_require__(97841);

module.exports = function (object, key, value) {
  var propertyKey = toPropertyKey(key);
  if (propertyKey in object) definePropertyModule.f(object, propertyKey, createPropertyDescriptor(0, value));
  else object[propertyKey] = value;
};


/***/ }),

/***/ 53087:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var anObject = __webpack_require__(34984);
var ordinaryToPrimitive = __webpack_require__(39629);

var TypeError = global.TypeError;

// `Date.prototype[@@toPrimitive](hint)` method implementation
// https://tc39.es/ecma262/#sec-date.prototype-@@toprimitive
module.exports = function (hint) {
  anObject(this);
  if (hint === 'string' || hint === 'default') hint = 'string';
  else if (hint !== 'number') throw TypeError('Incorrect hint');
  return ordinaryToPrimitive(this, hint);
};


/***/ }),

/***/ 97001:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var call = __webpack_require__(2834);
var IS_PURE = __webpack_require__(63432);
var FunctionName = __webpack_require__(7081);
var isCallable = __webpack_require__(94578);
var createIteratorConstructor = __webpack_require__(13945);
var getPrototypeOf = __webpack_require__(69548);
var setPrototypeOf = __webpack_require__(3840);
var setToStringTag = __webpack_require__(15216);
var createNonEnumerableProperty = __webpack_require__(48914);
var redefine = __webpack_require__(13711);
var wellKnownSymbol = __webpack_require__(38688);
var Iterators = __webpack_require__(15366);
var IteratorsCore = __webpack_require__(5844);

var PROPER_FUNCTION_NAME = FunctionName.PROPER;
var CONFIGURABLE_FUNCTION_NAME = FunctionName.CONFIGURABLE;
var IteratorPrototype = IteratorsCore.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS = IteratorsCore.BUGGY_SAFARI_ITERATORS;
var ITERATOR = wellKnownSymbol('iterator');
var KEYS = 'keys';
var VALUES = 'values';
var ENTRIES = 'entries';

var returnThis = function () { return this; };

module.exports = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS && KIND in IterablePrototype) return IterablePrototype[KIND];
    switch (KIND) {
      case KEYS: return function keys() { return new IteratorConstructor(this, KIND); };
      case VALUES: return function values() { return new IteratorConstructor(this, KIND); };
      case ENTRIES: return function entries() { return new IteratorConstructor(this, KIND); };
    } return function () { return new IteratorConstructor(this); };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR]
    || IterablePrototype['@@iterator']
    || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME == 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY;

  // fix native
  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf(anyNativeIterator.call(new Iterable()));
    if (CurrentIteratorPrototype !== Object.prototype && CurrentIteratorPrototype.next) {
      if (!IS_PURE && getPrototypeOf(CurrentIteratorPrototype) !== IteratorPrototype) {
        if (setPrototypeOf) {
          setPrototypeOf(CurrentIteratorPrototype, IteratorPrototype);
        } else if (!isCallable(CurrentIteratorPrototype[ITERATOR])) {
          redefine(CurrentIteratorPrototype, ITERATOR, returnThis);
        }
      }
      // Set @@toStringTag to native iterators
      setToStringTag(CurrentIteratorPrototype, TO_STRING_TAG, true, true);
      if (IS_PURE) Iterators[TO_STRING_TAG] = returnThis;
    }
  }

  // fix Array.prototype.{ values, @@iterator }.name in V8 / FF
  if (PROPER_FUNCTION_NAME && DEFAULT == VALUES && nativeIterator && nativeIterator.name !== VALUES) {
    if (!IS_PURE && CONFIGURABLE_FUNCTION_NAME) {
      createNonEnumerableProperty(IterablePrototype, 'name', VALUES);
    } else {
      INCORRECT_VALUES_NAME = true;
      defaultIterator = function values() { return call(nativeIterator, this); };
    }
  }

  // export additional methods
  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS),
      entries: getIterationMethod(ENTRIES)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        redefine(IterablePrototype, KEY, methods[KEY]);
      }
    } else $({ target: NAME, proto: true, forced: BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME }, methods);
  }

  // define iterator
  if ((!IS_PURE || FORCED) && IterablePrototype[ITERATOR] !== defaultIterator) {
    redefine(IterablePrototype, ITERATOR, defaultIterator, { name: DEFAULT });
  }
  Iterators[NAME] = defaultIterator;

  return methods;
};


/***/ }),

/***/ 46042:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var path = __webpack_require__(11206);
var hasOwn = __webpack_require__(20340);
var wrappedWellKnownSymbolModule = __webpack_require__(75960);
var defineProperty = (__webpack_require__(95892).f);

module.exports = function (NAME) {
  var Symbol = path.Symbol || (path.Symbol = {});
  if (!hasOwn(Symbol, NAME)) defineProperty(Symbol, NAME, {
    value: wrappedWellKnownSymbolModule.f(NAME)
  });
};


/***/ }),

/***/ 15567:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);

// Detect IE8's incomplete defineProperty implementation
module.exports = !fails(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, { get: function () { return 7; } })[1] != 7;
});


/***/ }),

/***/ 12072:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isObject = __webpack_require__(24517);

var document = global.document;
// typeof document.createElement is 'object' in old IE
var EXISTS = isObject(document) && isObject(document.createElement);

module.exports = function (it) {
  return EXISTS ? document.createElement(it) : {};
};


/***/ }),

/***/ 23327:
/***/ (function(module) {

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
module.exports = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};


/***/ }),

/***/ 67797:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

// in old WebKit versions, `element.classList` is not an instance of global `DOMTokenList`
var documentCreateElement = __webpack_require__(12072);

var classList = documentCreateElement('span').classList;
var DOMTokenListPrototype = classList && classList.constructor && classList.constructor.prototype;

module.exports = DOMTokenListPrototype === Object.prototype ? undefined : DOMTokenListPrototype;


/***/ }),

/***/ 3809:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var userAgent = __webpack_require__(40715);

var firefox = userAgent.match(/firefox\/(\d+)/i);

module.exports = !!firefox && +firefox[1];


/***/ }),

/***/ 3157:
/***/ (function(module) {

module.exports = typeof window == 'object';


/***/ }),

/***/ 21983:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var UA = __webpack_require__(40715);

module.exports = /MSIE|Trident/.test(UA);


/***/ }),

/***/ 70573:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var userAgent = __webpack_require__(40715);
var global = __webpack_require__(32010);

module.exports = /ipad|iphone|ipod/i.test(userAgent) && global.Pebble !== undefined;


/***/ }),

/***/ 17716:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var userAgent = __webpack_require__(40715);

module.exports = /(?:ipad|iphone|ipod).*applewebkit/i.test(userAgent);


/***/ }),

/***/ 95053:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var classof = __webpack_require__(93975);
var global = __webpack_require__(32010);

module.exports = classof(global.process) == 'process';


/***/ }),

/***/ 664:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var userAgent = __webpack_require__(40715);

module.exports = /web0s(?!.*chrome)/i.test(userAgent);


/***/ }),

/***/ 40715:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(38486);

module.exports = getBuiltIn('navigator', 'userAgent') || '';


/***/ }),

/***/ 70091:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var userAgent = __webpack_require__(40715);

var process = global.process;
var Deno = global.Deno;
var versions = process && process.versions || Deno && Deno.version;
var v8 = versions && versions.v8;
var match, version;

if (v8) {
  match = v8.split('.');
  // in old Chrome, versions of V8 isn't V8 = Chrome / 10
  // but their correct versions are not interesting for us
  version = match[0] > 0 && match[0] < 4 ? 1 : +(match[0] + match[1]);
}

// BrowserFS NodeJS `process` polyfill incorrectly set `.v8` to `0.0`
// so check `userAgent` even if `.v8` exists, but 0
if (!version && userAgent) {
  match = userAgent.match(/Edge\/(\d+)/);
  if (!match || match[1] >= 74) {
    match = userAgent.match(/Chrome\/(\d+)/);
    if (match) version = +match[1];
  }
}

module.exports = version;


/***/ }),

/***/ 41731:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var userAgent = __webpack_require__(40715);

var webkit = userAgent.match(/AppleWebKit\/(\d+)\./);

module.exports = !!webkit && +webkit[1];


/***/ }),

/***/ 2416:
/***/ (function(module) {

// IE8- don't enum bug keys
module.exports = [
  'constructor',
  'hasOwnProperty',
  'isPrototypeOf',
  'propertyIsEnumerable',
  'toLocaleString',
  'toString',
  'valueOf'
];


/***/ }),

/***/ 45144:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var createPropertyDescriptor = __webpack_require__(97841);

module.exports = !fails(function () {
  var error = Error('a');
  if (!('stack' in error)) return true;
  // eslint-disable-next-line es/no-object-defineproperty -- safe
  Object.defineProperty(error, 'stack', createPropertyDescriptor(1, 7));
  return error.stack !== 7;
});


/***/ }),

/***/ 56475:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var getOwnPropertyDescriptor = (__webpack_require__(72062).f);
var createNonEnumerableProperty = __webpack_require__(48914);
var redefine = __webpack_require__(13711);
var setGlobal = __webpack_require__(7421);
var copyConstructorProperties = __webpack_require__(2675);
var isForced = __webpack_require__(39599);

/*
  options.target      - name of the target object
  options.global      - target is the global object
  options.stat        - export as static methods of target
  options.proto       - export as prototype methods of target
  options.real        - real prototype method for the `pure` version
  options.forced      - export even if the native feature is available
  options.bind        - bind methods to the target, required for the `pure` version
  options.wrap        - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe      - use the simple assignment of property instead of delete + defineProperty
  options.sham        - add a flag to not completely full polyfills
  options.enumerable  - export as enumerable property
  options.noTargetGet - prevent calling a getter on target
  options.name        - the .name of the function if it does not match the key
*/
module.exports = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;
  if (GLOBAL) {
    target = global;
  } else if (STATIC) {
    target = global[TARGET] || setGlobal(TARGET, {});
  } else {
    target = (global[TARGET] || {}).prototype;
  }
  if (target) for (key in source) {
    sourceProperty = source[key];
    if (options.noTargetGet) {
      descriptor = getOwnPropertyDescriptor(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];
    FORCED = isForced(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced);
    // contained in target
    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty == typeof targetProperty) continue;
      copyConstructorProperties(sourceProperty, targetProperty);
    }
    // add a flag to not completely full polyfills
    if (options.sham || (targetProperty && targetProperty.sham)) {
      createNonEnumerableProperty(sourceProperty, 'sham', true);
    }
    // extend global
    redefine(target, key, sourceProperty, options);
  }
};


/***/ }),

/***/ 47044:
/***/ (function(module) {

module.exports = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};


/***/ }),

/***/ 11813:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

// TODO: Remove from `core-js@4` since it's moved to entry points
__webpack_require__(61726);
var uncurryThis = __webpack_require__(38347);
var redefine = __webpack_require__(13711);
var regexpExec = __webpack_require__(49820);
var fails = __webpack_require__(47044);
var wellKnownSymbol = __webpack_require__(38688);
var createNonEnumerableProperty = __webpack_require__(48914);

var SPECIES = wellKnownSymbol('species');
var RegExpPrototype = RegExp.prototype;

module.exports = function (KEY, exec, FORCED, SHAM) {
  var SYMBOL = wellKnownSymbol(KEY);

  var DELEGATES_TO_SYMBOL = !fails(function () {
    // String methods call symbol-named RegEp methods
    var O = {};
    O[SYMBOL] = function () { return 7; };
    return ''[KEY](O) != 7;
  });

  var DELEGATES_TO_EXEC = DELEGATES_TO_SYMBOL && !fails(function () {
    // Symbol-named RegExp methods call .exec
    var execCalled = false;
    var re = /a/;

    if (KEY === 'split') {
      // We can't use real regex here since it causes deoptimization
      // and serious performance degradation in V8
      // https://github.com/zloirock/core-js/issues/306
      re = {};
      // RegExp[@@split] doesn't call the regex's exec method, but first creates
      // a new one. We need to return the patched regex when creating the new one.
      re.constructor = {};
      re.constructor[SPECIES] = function () { return re; };
      re.flags = '';
      re[SYMBOL] = /./[SYMBOL];
    }

    re.exec = function () { execCalled = true; return null; };

    re[SYMBOL]('');
    return !execCalled;
  });

  if (
    !DELEGATES_TO_SYMBOL ||
    !DELEGATES_TO_EXEC ||
    FORCED
  ) {
    var uncurriedNativeRegExpMethod = uncurryThis(/./[SYMBOL]);
    var methods = exec(SYMBOL, ''[KEY], function (nativeMethod, regexp, str, arg2, forceStringMethod) {
      var uncurriedNativeMethod = uncurryThis(nativeMethod);
      var $exec = regexp.exec;
      if ($exec === regexpExec || $exec === RegExpPrototype.exec) {
        if (DELEGATES_TO_SYMBOL && !forceStringMethod) {
          // The native String method already delegates to @@method (this
          // polyfilled function), leasing to infinite recursion.
          // We avoid it by directly calling the native @@method method.
          return { done: true, value: uncurriedNativeRegExpMethod(regexp, str, arg2) };
        }
        return { done: true, value: uncurriedNativeMethod(str, regexp, arg2) };
      }
      return { done: false };
    });

    redefine(String.prototype, KEY, methods[0]);
    redefine(RegExpPrototype, SYMBOL, methods[1]);
  }

  if (SHAM) createNonEnumerableProperty(RegExpPrototype[SYMBOL], 'sham', true);
};


/***/ }),

/***/ 55481:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);

module.exports = !fails(function () {
  // eslint-disable-next-line es/no-object-isextensible, es/no-object-preventextensions -- required for testing
  return Object.isExtensible(Object.preventExtensions({}));
});


/***/ }),

/***/ 58448:
/***/ (function(module) {

var FunctionPrototype = Function.prototype;
var apply = FunctionPrototype.apply;
var bind = FunctionPrototype.bind;
var call = FunctionPrototype.call;

// eslint-disable-next-line es/no-reflect -- safe
module.exports = typeof Reflect == 'object' && Reflect.apply || (bind ? call.bind(apply) : function () {
  return call.apply(apply, arguments);
});


/***/ }),

/***/ 25567:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var aCallable = __webpack_require__(32631);

var bind = uncurryThis(uncurryThis.bind);

// optional / simple context binding
module.exports = function (fn, that) {
  aCallable(fn);
  return that === undefined ? fn : bind ? bind(fn, that) : function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};


/***/ }),

/***/ 2834:
/***/ (function(module) {

var call = Function.prototype.call;

module.exports = call.bind ? call.bind(call) : function () {
  return call.apply(call, arguments);
};


/***/ }),

/***/ 7081:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var hasOwn = __webpack_require__(20340);

var FunctionPrototype = Function.prototype;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getDescriptor = DESCRIPTORS && Object.getOwnPropertyDescriptor;

var EXISTS = hasOwn(FunctionPrototype, 'name');
// additional protection from minified / mangled / dropped function names
var PROPER = EXISTS && (function something() { /* empty */ }).name === 'something';
var CONFIGURABLE = EXISTS && (!DESCRIPTORS || (DESCRIPTORS && getDescriptor(FunctionPrototype, 'name').configurable));

module.exports = {
  EXISTS: EXISTS,
  PROPER: PROPER,
  CONFIGURABLE: CONFIGURABLE
};


/***/ }),

/***/ 38347:
/***/ (function(module) {

var FunctionPrototype = Function.prototype;
var bind = FunctionPrototype.bind;
var call = FunctionPrototype.call;
var callBind = bind && bind.bind(call);

module.exports = bind ? function (fn) {
  return fn && callBind(call, fn);
} : function (fn) {
  return fn && function () {
    return call.apply(fn, arguments);
  };
};


/***/ }),

/***/ 38486:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);

var aFunction = function (argument) {
  return isCallable(argument) ? argument : undefined;
};

module.exports = function (namespace, method) {
  return arguments.length < 2 ? aFunction(global[namespace]) : global[namespace] && global[namespace][method];
};


/***/ }),

/***/ 13872:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var classof = __webpack_require__(52564);
var getMethod = __webpack_require__(51839);
var Iterators = __webpack_require__(15366);
var wellKnownSymbol = __webpack_require__(38688);

var ITERATOR = wellKnownSymbol('iterator');

module.exports = function (it) {
  if (it != undefined) return getMethod(it, ITERATOR)
    || getMethod(it, '@@iterator')
    || Iterators[classof(it)];
};


/***/ }),

/***/ 15892:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var aCallable = __webpack_require__(32631);
var anObject = __webpack_require__(34984);
var tryToString = __webpack_require__(68664);
var getIteratorMethod = __webpack_require__(13872);

var TypeError = global.TypeError;

module.exports = function (argument, usingIterator) {
  var iteratorMethod = arguments.length < 2 ? getIteratorMethod(argument) : usingIterator;
  if (aCallable(iteratorMethod)) return anObject(call(iteratorMethod, argument));
  throw TypeError(tryToString(argument) + ' is not iterable');
};


/***/ }),

/***/ 51839:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var aCallable = __webpack_require__(32631);

// `GetMethod` abstract operation
// https://tc39.es/ecma262/#sec-getmethod
module.exports = function (V, P) {
  var func = V[P];
  return func == null ? undefined : aCallable(func);
};


/***/ }),

/***/ 29519:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var toObject = __webpack_require__(43162);

var floor = Math.floor;
var charAt = uncurryThis(''.charAt);
var replace = uncurryThis(''.replace);
var stringSlice = uncurryThis(''.slice);
var SUBSTITUTION_SYMBOLS = /\$([$&'`]|\d{1,2}|<[^>]*>)/g;
var SUBSTITUTION_SYMBOLS_NO_NAMED = /\$([$&'`]|\d{1,2})/g;

// `GetSubstitution` abstract operation
// https://tc39.es/ecma262/#sec-getsubstitution
module.exports = function (matched, str, position, captures, namedCaptures, replacement) {
  var tailPos = position + matched.length;
  var m = captures.length;
  var symbols = SUBSTITUTION_SYMBOLS_NO_NAMED;
  if (namedCaptures !== undefined) {
    namedCaptures = toObject(namedCaptures);
    symbols = SUBSTITUTION_SYMBOLS;
  }
  return replace(replacement, symbols, function (match, ch) {
    var capture;
    switch (charAt(ch, 0)) {
      case '$': return '$';
      case '&': return matched;
      case '`': return stringSlice(str, 0, position);
      case "'": return stringSlice(str, tailPos);
      case '<':
        capture = namedCaptures[stringSlice(ch, 1, -1)];
        break;
      default: // \d\d?
        var n = +ch;
        if (n === 0) return match;
        if (n > m) {
          var f = floor(n / 10);
          if (f === 0) return match;
          if (f <= m) return captures[f - 1] === undefined ? charAt(ch, 1) : captures[f - 1] + charAt(ch, 1);
          return match;
        }
        capture = captures[n - 1];
    }
    return capture === undefined ? '' : capture;
  });
};


/***/ }),

/***/ 32010:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var check = function (it) {
  return it && it.Math == Math && it;
};

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
module.exports =
  // eslint-disable-next-line es/no-global-this -- safe
  check(typeof globalThis == 'object' && globalThis) ||
  check(typeof window == 'object' && window) ||
  // eslint-disable-next-line no-restricted-globals -- safe
  check(typeof self == 'object' && self) ||
  check(typeof __webpack_require__.g == 'object' && __webpack_require__.g) ||
  // eslint-disable-next-line no-new-func -- fallback
  (function () { return this; })() || Function('return this')();


/***/ }),

/***/ 20340:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var toObject = __webpack_require__(43162);

var hasOwnProperty = uncurryThis({}.hasOwnProperty);

// `HasOwnProperty` abstract operation
// https://tc39.es/ecma262/#sec-hasownproperty
module.exports = Object.hasOwn || function hasOwn(it, key) {
  return hasOwnProperty(toObject(it), key);
};


/***/ }),

/***/ 90682:
/***/ (function(module) {

module.exports = {};


/***/ }),

/***/ 61144:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

module.exports = function (a, b) {
  var console = global.console;
  if (console && console.error) {
    arguments.length == 1 ? console.error(a) : console.error(a, b);
  }
};


/***/ }),

/***/ 520:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(38486);

module.exports = getBuiltIn('document', 'documentElement');


/***/ }),

/***/ 18904:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var fails = __webpack_require__(47044);
var createElement = __webpack_require__(12072);

// Thank's IE8 for his funny defineProperty
module.exports = !DESCRIPTORS && !fails(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- requied for testing
  return Object.defineProperty(createElement('div'), 'a', {
    get: function () { return 7; }
  }).a != 7;
});


/***/ }),

/***/ 64397:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

// IEEE754 conversions based on https://github.com/feross/ieee754
var global = __webpack_require__(32010);

var Array = global.Array;
var abs = Math.abs;
var pow = Math.pow;
var floor = Math.floor;
var log = Math.log;
var LN2 = Math.LN2;

var pack = function (number, mantissaLength, bytes) {
  var buffer = Array(bytes);
  var exponentLength = bytes * 8 - mantissaLength - 1;
  var eMax = (1 << exponentLength) - 1;
  var eBias = eMax >> 1;
  var rt = mantissaLength === 23 ? pow(2, -24) - pow(2, -77) : 0;
  var sign = number < 0 || number === 0 && 1 / number < 0 ? 1 : 0;
  var index = 0;
  var exponent, mantissa, c;
  number = abs(number);
  // eslint-disable-next-line no-self-compare -- NaN check
  if (number != number || number === Infinity) {
    // eslint-disable-next-line no-self-compare -- NaN check
    mantissa = number != number ? 1 : 0;
    exponent = eMax;
  } else {
    exponent = floor(log(number) / LN2);
    if (number * (c = pow(2, -exponent)) < 1) {
      exponent--;
      c *= 2;
    }
    if (exponent + eBias >= 1) {
      number += rt / c;
    } else {
      number += rt * pow(2, 1 - eBias);
    }
    if (number * c >= 2) {
      exponent++;
      c /= 2;
    }
    if (exponent + eBias >= eMax) {
      mantissa = 0;
      exponent = eMax;
    } else if (exponent + eBias >= 1) {
      mantissa = (number * c - 1) * pow(2, mantissaLength);
      exponent = exponent + eBias;
    } else {
      mantissa = number * pow(2, eBias - 1) * pow(2, mantissaLength);
      exponent = 0;
    }
  }
  for (; mantissaLength >= 8; buffer[index++] = mantissa & 255, mantissa /= 256, mantissaLength -= 8);
  exponent = exponent << mantissaLength | mantissa;
  exponentLength += mantissaLength;
  for (; exponentLength > 0; buffer[index++] = exponent & 255, exponent /= 256, exponentLength -= 8);
  buffer[--index] |= sign * 128;
  return buffer;
};

var unpack = function (buffer, mantissaLength) {
  var bytes = buffer.length;
  var exponentLength = bytes * 8 - mantissaLength - 1;
  var eMax = (1 << exponentLength) - 1;
  var eBias = eMax >> 1;
  var nBits = exponentLength - 7;
  var index = bytes - 1;
  var sign = buffer[index--];
  var exponent = sign & 127;
  var mantissa;
  sign >>= 7;
  for (; nBits > 0; exponent = exponent * 256 + buffer[index], index--, nBits -= 8);
  mantissa = exponent & (1 << -nBits) - 1;
  exponent >>= -nBits;
  nBits += mantissaLength;
  for (; nBits > 0; mantissa = mantissa * 256 + buffer[index], index--, nBits -= 8);
  if (exponent === 0) {
    exponent = 1 - eBias;
  } else if (exponent === eMax) {
    return mantissa ? NaN : sign ? -Infinity : Infinity;
  } else {
    mantissa = mantissa + pow(2, mantissaLength);
    exponent = exponent - eBias;
  } return (sign ? -1 : 1) * mantissa * pow(2, exponent - mantissaLength);
};

module.exports = {
  pack: pack,
  unpack: unpack
};


/***/ }),

/***/ 7514:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var fails = __webpack_require__(47044);
var classof = __webpack_require__(93975);

var Object = global.Object;
var split = uncurryThis(''.split);

// fallback for non-array-like ES3 and non-enumerable old V8 strings
module.exports = fails(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !Object('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof(it) == 'String' ? split(it, '') : Object(it);
} : Object;


/***/ }),

/***/ 51868:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);
var setPrototypeOf = __webpack_require__(3840);

// makes subclassing work correct for wrapped built-ins
module.exports = function ($this, dummy, Wrapper) {
  var NewTarget, NewTargetPrototype;
  if (
    // it can work only with native `setPrototypeOf`
    setPrototypeOf &&
    // we haven't completely correct pre-ES6 way for getting `new.target`, so use this
    isCallable(NewTarget = dummy.constructor) &&
    NewTarget !== Wrapper &&
    isObject(NewTargetPrototype = NewTarget.prototype) &&
    NewTargetPrototype !== Wrapper.prototype
  ) setPrototypeOf($this, NewTargetPrototype);
  return $this;
};


/***/ }),

/***/ 10447:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var isCallable = __webpack_require__(94578);
var store = __webpack_require__(55480);

var functionToString = uncurryThis(Function.toString);

// this helper broken in `core-js@3.4.1-3.4.4`, so we can't use `shared` helper
if (!isCallable(store.inspectSource)) {
  store.inspectSource = function (it) {
    return functionToString(it);
  };
}

module.exports = store.inspectSource;


/***/ }),

/***/ 87811:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var isObject = __webpack_require__(24517);
var createNonEnumerableProperty = __webpack_require__(48914);

// `InstallErrorCause` abstract operation
// https://tc39.es/proposal-error-cause/#sec-errorobjects-install-error-cause
module.exports = function (O, options) {
  if (isObject(options) && 'cause' in options) {
    createNonEnumerableProperty(O, 'cause', options.cause);
  }
};


/***/ }),

/***/ 62148:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var uncurryThis = __webpack_require__(38347);
var hiddenKeys = __webpack_require__(90682);
var isObject = __webpack_require__(24517);
var hasOwn = __webpack_require__(20340);
var defineProperty = (__webpack_require__(95892).f);
var getOwnPropertyNamesModule = __webpack_require__(6611);
var getOwnPropertyNamesExternalModule = __webpack_require__(8807);
var uid = __webpack_require__(46859);
var FREEZING = __webpack_require__(55481);

var REQUIRED = false;
var METADATA = uid('meta');
var id = 0;

// eslint-disable-next-line es/no-object-isextensible -- safe
var isExtensible = Object.isExtensible || function () {
  return true;
};

var setMetadata = function (it) {
  defineProperty(it, METADATA, { value: {
    objectID: 'O' + id++, // object ID
    weakData: {}          // weak collections IDs
  } });
};

var fastKey = function (it, create) {
  // return a primitive with prefix
  if (!isObject(it)) return typeof it == 'symbol' ? it : (typeof it == 'string' ? 'S' : 'P') + it;
  if (!hasOwn(it, METADATA)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return 'F';
    // not necessary to add metadata
    if (!create) return 'E';
    // add missing metadata
    setMetadata(it);
  // return object ID
  } return it[METADATA].objectID;
};

var getWeakData = function (it, create) {
  if (!hasOwn(it, METADATA)) {
    // can't set metadata to uncaught frozen object
    if (!isExtensible(it)) return true;
    // not necessary to add metadata
    if (!create) return false;
    // add missing metadata
    setMetadata(it);
  // return the store of weak collections IDs
  } return it[METADATA].weakData;
};

// add metadata on freeze-family methods calling
var onFreeze = function (it) {
  if (FREEZING && REQUIRED && isExtensible(it) && !hasOwn(it, METADATA)) setMetadata(it);
  return it;
};

var enable = function () {
  meta.enable = function () { /* empty */ };
  REQUIRED = true;
  var getOwnPropertyNames = getOwnPropertyNamesModule.f;
  var splice = uncurryThis([].splice);
  var test = {};
  test[METADATA] = 1;

  // prevent exposing of metadata key
  if (getOwnPropertyNames(test).length) {
    getOwnPropertyNamesModule.f = function (it) {
      var result = getOwnPropertyNames(it);
      for (var i = 0, length = result.length; i < length; i++) {
        if (result[i] === METADATA) {
          splice(result, i, 1);
          break;
        }
      } return result;
    };

    $({ target: 'Object', stat: true, forced: true }, {
      getOwnPropertyNames: getOwnPropertyNamesExternalModule.f
    });
  }
};

var meta = module.exports = {
  enable: enable,
  fastKey: fastKey,
  getWeakData: getWeakData,
  onFreeze: onFreeze
};

hiddenKeys[METADATA] = true;


/***/ }),

/***/ 70172:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var NATIVE_WEAK_MAP = __webpack_require__(26168);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var isObject = __webpack_require__(24517);
var createNonEnumerableProperty = __webpack_require__(48914);
var hasOwn = __webpack_require__(20340);
var shared = __webpack_require__(55480);
var sharedKey = __webpack_require__(82194);
var hiddenKeys = __webpack_require__(90682);

var OBJECT_ALREADY_INITIALIZED = 'Object already initialized';
var TypeError = global.TypeError;
var WeakMap = global.WeakMap;
var set, get, has;

var enforce = function (it) {
  return has(it) ? get(it) : set(it, {});
};

var getterFor = function (TYPE) {
  return function (it) {
    var state;
    if (!isObject(it) || (state = get(it)).type !== TYPE) {
      throw TypeError('Incompatible receiver, ' + TYPE + ' required');
    } return state;
  };
};

if (NATIVE_WEAK_MAP || shared.state) {
  var store = shared.state || (shared.state = new WeakMap());
  var wmget = uncurryThis(store.get);
  var wmhas = uncurryThis(store.has);
  var wmset = uncurryThis(store.set);
  set = function (it, metadata) {
    if (wmhas(store, it)) throw new TypeError(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    wmset(store, it, metadata);
    return metadata;
  };
  get = function (it) {
    return wmget(store, it) || {};
  };
  has = function (it) {
    return wmhas(store, it);
  };
} else {
  var STATE = sharedKey('state');
  hiddenKeys[STATE] = true;
  set = function (it, metadata) {
    if (hasOwn(it, STATE)) throw new TypeError(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    createNonEnumerableProperty(it, STATE, metadata);
    return metadata;
  };
  get = function (it) {
    return hasOwn(it, STATE) ? it[STATE] : {};
  };
  has = function (it) {
    return hasOwn(it, STATE);
  };
}

module.exports = {
  set: set,
  get: get,
  has: has,
  enforce: enforce,
  getterFor: getterFor
};


/***/ }),

/***/ 89564:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);
var Iterators = __webpack_require__(15366);

var ITERATOR = wellKnownSymbol('iterator');
var ArrayPrototype = Array.prototype;

// check on default Array iterator
module.exports = function (it) {
  return it !== undefined && (Iterators.Array === it || ArrayPrototype[ITERATOR] === it);
};


/***/ }),

/***/ 59113:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var classof = __webpack_require__(93975);

// `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe
module.exports = Array.isArray || function isArray(argument) {
  return classof(argument) == 'Array';
};


/***/ }),

/***/ 94578:
/***/ (function(module) {

// `IsCallable` abstract operation
// https://tc39.es/ecma262/#sec-iscallable
module.exports = function (argument) {
  return typeof argument == 'function';
};


/***/ }),

/***/ 20884:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var fails = __webpack_require__(47044);
var isCallable = __webpack_require__(94578);
var classof = __webpack_require__(52564);
var getBuiltIn = __webpack_require__(38486);
var inspectSource = __webpack_require__(10447);

var noop = function () { /* empty */ };
var empty = [];
var construct = getBuiltIn('Reflect', 'construct');
var constructorRegExp = /^\s*(?:class|function)\b/;
var exec = uncurryThis(constructorRegExp.exec);
var INCORRECT_TO_STRING = !constructorRegExp.exec(noop);

var isConstructorModern = function (argument) {
  if (!isCallable(argument)) return false;
  try {
    construct(noop, empty, argument);
    return true;
  } catch (error) {
    return false;
  }
};

var isConstructorLegacy = function (argument) {
  if (!isCallable(argument)) return false;
  switch (classof(argument)) {
    case 'AsyncFunction':
    case 'GeneratorFunction':
    case 'AsyncGeneratorFunction': return false;
    // we can't check .prototype since constructors produced by .bind haven't it
  } return INCORRECT_TO_STRING || !!exec(constructorRegExp, inspectSource(argument));
};

// `IsConstructor` abstract operation
// https://tc39.es/ecma262/#sec-isconstructor
module.exports = !construct || fails(function () {
  var called;
  return isConstructorModern(isConstructorModern.call)
    || !isConstructorModern(Object)
    || !isConstructorModern(function () { called = true; })
    || called;
}) ? isConstructorLegacy : isConstructorModern;


/***/ }),

/***/ 39599:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var isCallable = __webpack_require__(94578);

var replacement = /#|\.prototype\./;

var isForced = function (feature, detection) {
  var value = data[normalize(feature)];
  return value == POLYFILL ? true
    : value == NATIVE ? false
    : isCallable(detection) ? fails(detection)
    : !!detection;
};

var normalize = isForced.normalize = function (string) {
  return String(string).replace(replacement, '.').toLowerCase();
};

var data = isForced.data = {};
var NATIVE = isForced.NATIVE = 'N';
var POLYFILL = isForced.POLYFILL = 'P';

module.exports = isForced;


/***/ }),

/***/ 17506:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var isObject = __webpack_require__(24517);

var floor = Math.floor;

// `IsIntegralNumber` abstract operation
// https://tc39.es/ecma262/#sec-isintegralnumber
// eslint-disable-next-line es/no-number-isinteger -- safe
module.exports = Number.isInteger || function isInteger(it) {
  return !isObject(it) && isFinite(it) && floor(it) === it;
};


/***/ }),

/***/ 24517:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var isCallable = __webpack_require__(94578);

module.exports = function (it) {
  return typeof it == 'object' ? it !== null : isCallable(it);
};


/***/ }),

/***/ 63432:
/***/ (function(module) {

module.exports = false;


/***/ }),

/***/ 28831:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var isObject = __webpack_require__(24517);
var classof = __webpack_require__(93975);
var wellKnownSymbol = __webpack_require__(38688);

var MATCH = wellKnownSymbol('match');

// `IsRegExp` abstract operation
// https://tc39.es/ecma262/#sec-isregexp
module.exports = function (it) {
  var isRegExp;
  return isObject(it) && ((isRegExp = it[MATCH]) !== undefined ? !!isRegExp : classof(it) == 'RegExp');
};


/***/ }),

/***/ 46290:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var getBuiltIn = __webpack_require__(38486);
var isCallable = __webpack_require__(94578);
var isPrototypeOf = __webpack_require__(70176);
var USE_SYMBOL_AS_UID = __webpack_require__(9567);

var Object = global.Object;

module.exports = USE_SYMBOL_AS_UID ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  var $Symbol = getBuiltIn('Symbol');
  return isCallable($Symbol) && isPrototypeOf($Symbol.prototype, Object(it));
};


/***/ }),

/***/ 80383:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var bind = __webpack_require__(25567);
var call = __webpack_require__(2834);
var anObject = __webpack_require__(34984);
var tryToString = __webpack_require__(68664);
var isArrayIteratorMethod = __webpack_require__(89564);
var lengthOfArrayLike = __webpack_require__(45495);
var isPrototypeOf = __webpack_require__(70176);
var getIterator = __webpack_require__(15892);
var getIteratorMethod = __webpack_require__(13872);
var iteratorClose = __webpack_require__(50194);

var TypeError = global.TypeError;

var Result = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var ResultPrototype = Result.prototype;

module.exports = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind(unboundFunction, that);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose(iterator, 'normal', condition);
    return new Result(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    } return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod(iterable);
    if (!iterFn) throw TypeError(tryToString(iterable) + ' is not iterable');
    // optimisation for array iterators
    if (isArrayIteratorMethod(iterFn)) {
      for (index = 0, length = lengthOfArrayLike(iterable); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && isPrototypeOf(ResultPrototype, result)) return result;
      } return new Result(false);
    }
    iterator = getIterator(iterable, iterFn);
  }

  next = iterator.next;
  while (!(step = call(next, iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose(iterator, 'throw', error);
    }
    if (typeof result == 'object' && result && isPrototypeOf(ResultPrototype, result)) return result;
  } return new Result(false);
};


/***/ }),

/***/ 50194:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var call = __webpack_require__(2834);
var anObject = __webpack_require__(34984);
var getMethod = __webpack_require__(51839);

module.exports = function (iterator, kind, value) {
  var innerResult, innerError;
  anObject(iterator);
  try {
    innerResult = getMethod(iterator, 'return');
    if (!innerResult) {
      if (kind === 'throw') throw value;
      return value;
    }
    innerResult = call(innerResult, iterator);
  } catch (error) {
    innerError = true;
    innerResult = error;
  }
  if (kind === 'throw') throw value;
  if (innerError) throw innerResult;
  anObject(innerResult);
  return value;
};


/***/ }),

/***/ 5844:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var fails = __webpack_require__(47044);
var isCallable = __webpack_require__(94578);
var create = __webpack_require__(10819);
var getPrototypeOf = __webpack_require__(69548);
var redefine = __webpack_require__(13711);
var wellKnownSymbol = __webpack_require__(38688);
var IS_PURE = __webpack_require__(63432);

var ITERATOR = wellKnownSymbol('iterator');
var BUGGY_SAFARI_ITERATORS = false;

// `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object
var IteratorPrototype, PrototypeOfArrayIteratorPrototype, arrayIterator;

/* eslint-disable es/no-array-prototype-keys -- safe */
if ([].keys) {
  arrayIterator = [].keys();
  // Safari 8 has buggy iterators w/o `next`
  if (!('next' in arrayIterator)) BUGGY_SAFARI_ITERATORS = true;
  else {
    PrototypeOfArrayIteratorPrototype = getPrototypeOf(getPrototypeOf(arrayIterator));
    if (PrototypeOfArrayIteratorPrototype !== Object.prototype) IteratorPrototype = PrototypeOfArrayIteratorPrototype;
  }
}

var NEW_ITERATOR_PROTOTYPE = IteratorPrototype == undefined || fails(function () {
  var test = {};
  // FF44- legacy iterators case
  return IteratorPrototype[ITERATOR].call(test) !== test;
});

if (NEW_ITERATOR_PROTOTYPE) IteratorPrototype = {};
else if (IS_PURE) IteratorPrototype = create(IteratorPrototype);

// `%IteratorPrototype%[@@iterator]()` method
// https://tc39.es/ecma262/#sec-%iteratorprototype%-@@iterator
if (!isCallable(IteratorPrototype[ITERATOR])) {
  redefine(IteratorPrototype, ITERATOR, function () {
    return this;
  });
}

module.exports = {
  IteratorPrototype: IteratorPrototype,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS
};


/***/ }),

/***/ 15366:
/***/ (function(module) {

module.exports = {};


/***/ }),

/***/ 45495:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toLength = __webpack_require__(23417);

// `LengthOfArrayLike` abstract operation
// https://tc39.es/ecma262/#sec-lengthofarraylike
module.exports = function (obj) {
  return toLength(obj.length);
};


/***/ }),

/***/ 59804:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var bind = __webpack_require__(25567);
var getOwnPropertyDescriptor = (__webpack_require__(72062).f);
var macrotask = (__webpack_require__(6616).set);
var IS_IOS = __webpack_require__(17716);
var IS_IOS_PEBBLE = __webpack_require__(70573);
var IS_WEBOS_WEBKIT = __webpack_require__(664);
var IS_NODE = __webpack_require__(95053);

var MutationObserver = global.MutationObserver || global.WebKitMutationObserver;
var document = global.document;
var process = global.process;
var Promise = global.Promise;
// Node.js 11 shows ExperimentalWarning on getting `queueMicrotask`
var queueMicrotaskDescriptor = getOwnPropertyDescriptor(global, 'queueMicrotask');
var queueMicrotask = queueMicrotaskDescriptor && queueMicrotaskDescriptor.value;

var flush, head, last, notify, toggle, node, promise, then;

// modern engines have queueMicrotask method
if (!queueMicrotask) {
  flush = function () {
    var parent, fn;
    if (IS_NODE && (parent = process.domain)) parent.exit();
    while (head) {
      fn = head.fn;
      head = head.next;
      try {
        fn();
      } catch (error) {
        if (head) notify();
        else last = undefined;
        throw error;
      }
    } last = undefined;
    if (parent) parent.enter();
  };

  // browsers with MutationObserver, except iOS - https://github.com/zloirock/core-js/issues/339
  // also except WebOS Webkit https://github.com/zloirock/core-js/issues/898
  if (!IS_IOS && !IS_NODE && !IS_WEBOS_WEBKIT && MutationObserver && document) {
    toggle = true;
    node = document.createTextNode('');
    new MutationObserver(flush).observe(node, { characterData: true });
    notify = function () {
      node.data = toggle = !toggle;
    };
  // environments with maybe non-completely correct, but existent Promise
  } else if (!IS_IOS_PEBBLE && Promise && Promise.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    promise = Promise.resolve(undefined);
    // workaround of WebKit ~ iOS Safari 10.1 bug
    promise.constructor = Promise;
    then = bind(promise.then, promise);
    notify = function () {
      then(flush);
    };
  // Node.js without promises
  } else if (IS_NODE) {
    notify = function () {
      process.nextTick(flush);
    };
  // for other environments - macrotask based on:
  // - setImmediate
  // - MessageChannel
  // - window.postMessag
  // - onreadystatechange
  // - setTimeout
  } else {
    // strange IE + webpack dev server bug - use .bind(global)
    macrotask = bind(macrotask, global);
    notify = function () {
      macrotask(flush);
    };
  }
}

module.exports = queueMicrotask || function (fn) {
  var task = { fn: fn, next: undefined };
  if (last) last.next = task;
  if (!head) {
    head = task;
    notify();
  } last = task;
};


/***/ }),

/***/ 5155:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

module.exports = global.Promise;


/***/ }),

/***/ 46887:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* eslint-disable es/no-symbol -- required for testing */
var V8_VERSION = __webpack_require__(70091);
var fails = __webpack_require__(47044);

// eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing
module.exports = !!Object.getOwnPropertySymbols && !fails(function () {
  var symbol = Symbol();
  // Chrome 38 Symbol has incorrect toString conversion
  // `get-own-property-symbols` polyfill symbols converted to object are not Symbol instances
  return !String(symbol) || !(Object(symbol) instanceof Symbol) ||
    // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
    !Symbol.sham && V8_VERSION && V8_VERSION < 41;
});


/***/ }),

/***/ 26168:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);
var inspectSource = __webpack_require__(10447);

var WeakMap = global.WeakMap;

module.exports = isCallable(WeakMap) && /native code/.test(inspectSource(WeakMap));


/***/ }),

/***/ 56614:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var aCallable = __webpack_require__(32631);

var PromiseCapability = function (C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw TypeError('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aCallable(resolve);
  this.reject = aCallable(reject);
};

// `NewPromiseCapability` abstract operation
// https://tc39.es/ecma262/#sec-newpromisecapability
module.exports.f = function (C) {
  return new PromiseCapability(C);
};


/***/ }),

/***/ 86392:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toString = __webpack_require__(25096);

module.exports = function (argument, $default) {
  return argument === undefined ? arguments.length < 2 ? '' : $default : toString(argument);
};


/***/ }),

/***/ 93666:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isRegExp = __webpack_require__(28831);

var TypeError = global.TypeError;

module.exports = function (it) {
  if (isRegExp(it)) {
    throw TypeError("The method doesn't accept regular expressions");
  } return it;
};


/***/ }),

/***/ 59805:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

var globalIsFinite = global.isFinite;

// `Number.isFinite` method
// https://tc39.es/ecma262/#sec-number.isfinite
// eslint-disable-next-line es/no-number-isfinite -- safe
module.exports = Number.isFinite || function isFinite(it) {
  return typeof it == 'number' && globalIsFinite(it);
};


/***/ }),

/***/ 87146:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var DESCRIPTORS = __webpack_require__(15567);
var uncurryThis = __webpack_require__(38347);
var call = __webpack_require__(2834);
var fails = __webpack_require__(47044);
var objectKeys = __webpack_require__(84675);
var getOwnPropertySymbolsModule = __webpack_require__(61146);
var propertyIsEnumerableModule = __webpack_require__(55574);
var toObject = __webpack_require__(43162);
var IndexedObject = __webpack_require__(7514);

// eslint-disable-next-line es/no-object-assign -- safe
var $assign = Object.assign;
// eslint-disable-next-line es/no-object-defineproperty -- required for testing
var defineProperty = Object.defineProperty;
var concat = uncurryThis([].concat);

// `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
module.exports = !$assign || fails(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS && $assign({ b: 1 }, $assign(defineProperty({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), { b: 2 })).b !== 1) return true;
  // should work with symbols and should have deterministic property order (V8 bug)
  var A = {};
  var B = {};
  // eslint-disable-next-line es/no-symbol -- safe
  var symbol = Symbol();
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) { B[chr] = chr; });
  return $assign({}, A)[symbol] != 7 || objectKeys($assign({}, B)).join('') != alphabet;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars -- required for `.length`
  var T = toObject(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  var propertyIsEnumerable = propertyIsEnumerableModule.f;
  while (argumentsLength > index) {
    var S = IndexedObject(arguments[index++]);
    var keys = getOwnPropertySymbols ? concat(objectKeys(S), getOwnPropertySymbols(S)) : objectKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS || call(propertyIsEnumerable, S, key)) T[key] = S[key];
    }
  } return T;
} : $assign;


/***/ }),

/***/ 10819:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* global ActiveXObject -- old IE, WSH */
var anObject = __webpack_require__(34984);
var defineProperties = __webpack_require__(10196);
var enumBugKeys = __webpack_require__(2416);
var hiddenKeys = __webpack_require__(90682);
var html = __webpack_require__(520);
var documentCreateElement = __webpack_require__(12072);
var sharedKey = __webpack_require__(82194);

var GT = '>';
var LT = '<';
var PROTOTYPE = 'prototype';
var SCRIPT = 'script';
var IE_PROTO = sharedKey('IE_PROTO');

var EmptyConstructor = function () { /* empty */ };

var scriptTag = function (content) {
  return LT + SCRIPT + GT + content + LT + '/' + SCRIPT + GT;
};

// Create object with fake `null` prototype: use ActiveX Object with cleared prototype
var NullProtoObjectViaActiveX = function (activeXDocument) {
  activeXDocument.write(scriptTag(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak
  return temp;
};

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var NullProtoObjectViaIFrame = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement('iframe');
  var JS = 'java' + SCRIPT + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html.appendChild(iframe);
  // https://github.com/zloirock/core-js/issues/475
  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
};

// Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug
var activeXDocument;
var NullProtoObject = function () {
  try {
    activeXDocument = new ActiveXObject('htmlfile');
  } catch (error) { /* ignore */ }
  NullProtoObject = typeof document != 'undefined'
    ? document.domain && activeXDocument
      ? NullProtoObjectViaActiveX(activeXDocument) // old IE
      : NullProtoObjectViaIFrame()
    : NullProtoObjectViaActiveX(activeXDocument); // WSH
  var length = enumBugKeys.length;
  while (length--) delete NullProtoObject[PROTOTYPE][enumBugKeys[length]];
  return NullProtoObject();
};

hiddenKeys[IE_PROTO] = true;

// `Object.create` method
// https://tc39.es/ecma262/#sec-object.create
module.exports = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    EmptyConstructor[PROTOTYPE] = anObject(O);
    result = new EmptyConstructor();
    EmptyConstructor[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO] = O;
  } else result = NullProtoObject();
  return Properties === undefined ? result : defineProperties(result, Properties);
};


/***/ }),

/***/ 10196:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var definePropertyModule = __webpack_require__(95892);
var anObject = __webpack_require__(34984);
var toIndexedObject = __webpack_require__(98086);
var objectKeys = __webpack_require__(84675);

// `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe
module.exports = DESCRIPTORS ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject(O);
  var props = toIndexedObject(Properties);
  var keys = objectKeys(Properties);
  var length = keys.length;
  var index = 0;
  var key;
  while (length > index) definePropertyModule.f(O, key = keys[index++], props[key]);
  return O;
};


/***/ }),

/***/ 95892:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var global = __webpack_require__(32010);
var DESCRIPTORS = __webpack_require__(15567);
var IE8_DOM_DEFINE = __webpack_require__(18904);
var anObject = __webpack_require__(34984);
var toPropertyKey = __webpack_require__(63918);

var TypeError = global.TypeError;
// eslint-disable-next-line es/no-object-defineproperty -- safe
var $defineProperty = Object.defineProperty;

// `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty
exports.f = DESCRIPTORS ? $defineProperty : function defineProperty(O, P, Attributes) {
  anObject(O);
  P = toPropertyKey(P);
  anObject(Attributes);
  if (IE8_DOM_DEFINE) try {
    return $defineProperty(O, P, Attributes);
  } catch (error) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw TypeError('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};


/***/ }),

/***/ 72062:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var call = __webpack_require__(2834);
var propertyIsEnumerableModule = __webpack_require__(55574);
var createPropertyDescriptor = __webpack_require__(97841);
var toIndexedObject = __webpack_require__(98086);
var toPropertyKey = __webpack_require__(63918);
var hasOwn = __webpack_require__(20340);
var IE8_DOM_DEFINE = __webpack_require__(18904);

// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var $getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor
exports.f = DESCRIPTORS ? $getOwnPropertyDescriptor : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject(O);
  P = toPropertyKey(P);
  if (IE8_DOM_DEFINE) try {
    return $getOwnPropertyDescriptor(O, P);
  } catch (error) { /* empty */ }
  if (hasOwn(O, P)) return createPropertyDescriptor(!call(propertyIsEnumerableModule.f, O, P), O[P]);
};


/***/ }),

/***/ 8807:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* eslint-disable es/no-object-getownpropertynames -- safe */
var classof = __webpack_require__(93975);
var toIndexedObject = __webpack_require__(98086);
var $getOwnPropertyNames = (__webpack_require__(6611).f);
var arraySlice = __webpack_require__(73163);

var windowNames = typeof window == 'object' && window && Object.getOwnPropertyNames
  ? Object.getOwnPropertyNames(window) : [];

var getWindowNames = function (it) {
  try {
    return $getOwnPropertyNames(it);
  } catch (error) {
    return arraySlice(windowNames);
  }
};

// fallback for IE11 buggy Object.getOwnPropertyNames with iframe and window
module.exports.f = function getOwnPropertyNames(it) {
  return windowNames && classof(it) == 'Window'
    ? getWindowNames(it)
    : $getOwnPropertyNames(toIndexedObject(it));
};


/***/ }),

/***/ 6611:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var internalObjectKeys = __webpack_require__(64429);
var enumBugKeys = __webpack_require__(2416);

var hiddenKeys = enumBugKeys.concat('length', 'prototype');

// `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe
exports.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys(O, hiddenKeys);
};


/***/ }),

/***/ 61146:
/***/ (function(__unused_webpack_module, exports) {

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
exports.f = Object.getOwnPropertySymbols;


/***/ }),

/***/ 69548:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var hasOwn = __webpack_require__(20340);
var isCallable = __webpack_require__(94578);
var toObject = __webpack_require__(43162);
var sharedKey = __webpack_require__(82194);
var CORRECT_PROTOTYPE_GETTER = __webpack_require__(68494);

var IE_PROTO = sharedKey('IE_PROTO');
var Object = global.Object;
var ObjectPrototype = Object.prototype;

// `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
module.exports = CORRECT_PROTOTYPE_GETTER ? Object.getPrototypeOf : function (O) {
  var object = toObject(O);
  if (hasOwn(object, IE_PROTO)) return object[IE_PROTO];
  var constructor = object.constructor;
  if (isCallable(constructor) && object instanceof constructor) {
    return constructor.prototype;
  } return object instanceof Object ? ObjectPrototype : null;
};


/***/ }),

/***/ 70176:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);

module.exports = uncurryThis({}.isPrototypeOf);


/***/ }),

/***/ 64429:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var hasOwn = __webpack_require__(20340);
var toIndexedObject = __webpack_require__(98086);
var indexOf = (__webpack_require__(12636).indexOf);
var hiddenKeys = __webpack_require__(90682);

var push = uncurryThis([].push);

module.exports = function (object, names) {
  var O = toIndexedObject(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) !hasOwn(hiddenKeys, key) && hasOwn(O, key) && push(result, key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (hasOwn(O, key = names[i++])) {
    ~indexOf(result, key) || push(result, key);
  }
  return result;
};


/***/ }),

/***/ 84675:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var internalObjectKeys = __webpack_require__(64429);
var enumBugKeys = __webpack_require__(2416);

// `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe
module.exports = Object.keys || function keys(O) {
  return internalObjectKeys(O, enumBugKeys);
};


/***/ }),

/***/ 55574:
/***/ (function(__unused_webpack_module, exports) {

"use strict";

var $propertyIsEnumerable = {}.propertyIsEnumerable;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// Nashorn ~ JDK8 bug
var NASHORN_BUG = getOwnPropertyDescriptor && !$propertyIsEnumerable.call({ 1: 2 }, 1);

// `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable
exports.f = NASHORN_BUG ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable;


/***/ }),

/***/ 3840:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* eslint-disable no-proto -- safe */
var uncurryThis = __webpack_require__(38347);
var anObject = __webpack_require__(34984);
var aPossiblePrototype = __webpack_require__(58659);

// `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe
module.exports = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;
  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    setter = uncurryThis(Object.getOwnPropertyDescriptor(Object.prototype, '__proto__').set);
    setter(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) { /* empty */ }
  return function setPrototypeOf(O, proto) {
    anObject(O);
    aPossiblePrototype(proto);
    if (CORRECT_SETTER) setter(O, proto);
    else O.__proto__ = proto;
    return O;
  };
}() : undefined);


/***/ }),

/***/ 80754:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var uncurryThis = __webpack_require__(38347);
var objectKeys = __webpack_require__(84675);
var toIndexedObject = __webpack_require__(98086);
var $propertyIsEnumerable = (__webpack_require__(55574).f);

var propertyIsEnumerable = uncurryThis($propertyIsEnumerable);
var push = uncurryThis([].push);

// `Object.{ entries, values }` methods implementation
var createMethod = function (TO_ENTRIES) {
  return function (it) {
    var O = toIndexedObject(it);
    var keys = objectKeys(O);
    var length = keys.length;
    var i = 0;
    var result = [];
    var key;
    while (length > i) {
      key = keys[i++];
      if (!DESCRIPTORS || propertyIsEnumerable(O, key)) {
        push(result, TO_ENTRIES ? [key, O[key]] : O[key]);
      }
    }
    return result;
  };
};

module.exports = {
  // `Object.entries` method
  // https://tc39.es/ecma262/#sec-object.entries
  entries: createMethod(true),
  // `Object.values` method
  // https://tc39.es/ecma262/#sec-object.values
  values: createMethod(false)
};


/***/ }),

/***/ 52598:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var TO_STRING_TAG_SUPPORT = __webpack_require__(24663);
var classof = __webpack_require__(52564);

// `Object.prototype.toString` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.tostring
module.exports = TO_STRING_TAG_SUPPORT ? {}.toString : function toString() {
  return '[object ' + classof(this) + ']';
};


/***/ }),

/***/ 39629:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);

var TypeError = global.TypeError;

// `OrdinaryToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-ordinarytoprimitive
module.exports = function (input, pref) {
  var fn, val;
  if (pref === 'string' && isCallable(fn = input.toString) && !isObject(val = call(fn, input))) return val;
  if (isCallable(fn = input.valueOf) && !isObject(val = call(fn, input))) return val;
  if (pref !== 'string' && isCallable(fn = input.toString) && !isObject(val = call(fn, input))) return val;
  throw TypeError("Can't convert object to primitive value");
};


/***/ }),

/***/ 21594:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var getBuiltIn = __webpack_require__(38486);
var uncurryThis = __webpack_require__(38347);
var getOwnPropertyNamesModule = __webpack_require__(6611);
var getOwnPropertySymbolsModule = __webpack_require__(61146);
var anObject = __webpack_require__(34984);

var concat = uncurryThis([].concat);

// all object keys, includes non-enumerable and symbols
module.exports = getBuiltIn('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule.f(anObject(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  return getOwnPropertySymbols ? concat(keys, getOwnPropertySymbols(it)) : keys;
};


/***/ }),

/***/ 11206:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

module.exports = global;


/***/ }),

/***/ 61900:
/***/ (function(module) {

module.exports = function (exec) {
  try {
    return { error: false, value: exec() };
  } catch (error) {
    return { error: true, value: error };
  }
};


/***/ }),

/***/ 28617:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var anObject = __webpack_require__(34984);
var isObject = __webpack_require__(24517);
var newPromiseCapability = __webpack_require__(56614);

module.exports = function (C, x) {
  anObject(C);
  if (isObject(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};


/***/ }),

/***/ 15341:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var redefine = __webpack_require__(13711);

module.exports = function (target, src, options) {
  for (var key in src) redefine(target, key, src[key], options);
  return target;
};


/***/ }),

/***/ 13711:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var isCallable = __webpack_require__(94578);
var hasOwn = __webpack_require__(20340);
var createNonEnumerableProperty = __webpack_require__(48914);
var setGlobal = __webpack_require__(7421);
var inspectSource = __webpack_require__(10447);
var InternalStateModule = __webpack_require__(70172);
var CONFIGURABLE_FUNCTION_NAME = (__webpack_require__(7081).CONFIGURABLE);

var getInternalState = InternalStateModule.get;
var enforceInternalState = InternalStateModule.enforce;
var TEMPLATE = String(String).split('String');

(module.exports = function (O, key, value, options) {
  var unsafe = options ? !!options.unsafe : false;
  var simple = options ? !!options.enumerable : false;
  var noTargetGet = options ? !!options.noTargetGet : false;
  var name = options && options.name !== undefined ? options.name : key;
  var state;
  if (isCallable(value)) {
    if (String(name).slice(0, 7) === 'Symbol(') {
      name = '[' + String(name).replace(/^Symbol\(([^)]*)\)/, '$1') + ']';
    }
    if (!hasOwn(value, 'name') || (CONFIGURABLE_FUNCTION_NAME && value.name !== name)) {
      createNonEnumerableProperty(value, 'name', name);
    }
    state = enforceInternalState(value);
    if (!state.source) {
      state.source = TEMPLATE.join(typeof name == 'string' ? name : '');
    }
  }
  if (O === global) {
    if (simple) O[key] = value;
    else setGlobal(key, value);
    return;
  } else if (!unsafe) {
    delete O[key];
  } else if (!noTargetGet && O[key]) {
    simple = true;
  }
  if (simple) O[key] = value;
  else createNonEnumerableProperty(O, key, value);
// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
})(Function.prototype, 'toString', function toString() {
  return isCallable(this) && getInternalState(this).source || inspectSource(this);
});


/***/ }),

/***/ 66723:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var anObject = __webpack_require__(34984);
var isCallable = __webpack_require__(94578);
var classof = __webpack_require__(93975);
var regexpExec = __webpack_require__(49820);

var TypeError = global.TypeError;

// `RegExpExec` abstract operation
// https://tc39.es/ecma262/#sec-regexpexec
module.exports = function (R, S) {
  var exec = R.exec;
  if (isCallable(exec)) {
    var result = call(exec, R, S);
    if (result !== null) anObject(result);
    return result;
  }
  if (classof(R) === 'RegExp') return call(regexpExec, R, S);
  throw TypeError('RegExp#exec called on incompatible receiver');
};


/***/ }),

/***/ 49820:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

/* eslint-disable regexp/no-empty-capturing-group, regexp/no-empty-group, regexp/no-lazy-ends -- testing */
/* eslint-disable regexp/no-useless-quantifier -- testing */
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var toString = __webpack_require__(25096);
var regexpFlags = __webpack_require__(21182);
var stickyHelpers = __webpack_require__(74846);
var shared = __webpack_require__(464);
var create = __webpack_require__(10819);
var getInternalState = (__webpack_require__(70172).get);
var UNSUPPORTED_DOT_ALL = __webpack_require__(84030);
var UNSUPPORTED_NCG = __webpack_require__(97739);

var nativeReplace = shared('native-string-replace', String.prototype.replace);
var nativeExec = RegExp.prototype.exec;
var patchedExec = nativeExec;
var charAt = uncurryThis(''.charAt);
var indexOf = uncurryThis(''.indexOf);
var replace = uncurryThis(''.replace);
var stringSlice = uncurryThis(''.slice);

var UPDATES_LAST_INDEX_WRONG = (function () {
  var re1 = /a/;
  var re2 = /b*/g;
  call(nativeExec, re1, 'a');
  call(nativeExec, re2, 'a');
  return re1.lastIndex !== 0 || re2.lastIndex !== 0;
})();

var UNSUPPORTED_Y = stickyHelpers.UNSUPPORTED_Y || stickyHelpers.BROKEN_CARET;

// nonparticipating capturing group, copied from es5-shim's String#split patch.
var NPCG_INCLUDED = /()??/.exec('')[1] !== undefined;

var PATCH = UPDATES_LAST_INDEX_WRONG || NPCG_INCLUDED || UNSUPPORTED_Y || UNSUPPORTED_DOT_ALL || UNSUPPORTED_NCG;

if (PATCH) {
  // eslint-disable-next-line max-statements -- TODO
  patchedExec = function exec(string) {
    var re = this;
    var state = getInternalState(re);
    var str = toString(string);
    var raw = state.raw;
    var result, reCopy, lastIndex, match, i, object, group;

    if (raw) {
      raw.lastIndex = re.lastIndex;
      result = call(patchedExec, raw, str);
      re.lastIndex = raw.lastIndex;
      return result;
    }

    var groups = state.groups;
    var sticky = UNSUPPORTED_Y && re.sticky;
    var flags = call(regexpFlags, re);
    var source = re.source;
    var charsAdded = 0;
    var strCopy = str;

    if (sticky) {
      flags = replace(flags, 'y', '');
      if (indexOf(flags, 'g') === -1) {
        flags += 'g';
      }

      strCopy = stringSlice(str, re.lastIndex);
      // Support anchored sticky behavior.
      if (re.lastIndex > 0 && (!re.multiline || re.multiline && charAt(str, re.lastIndex - 1) !== '\n')) {
        source = '(?: ' + source + ')';
        strCopy = ' ' + strCopy;
        charsAdded++;
      }
      // ^(? + rx + ) is needed, in combination with some str slicing, to
      // simulate the 'y' flag.
      reCopy = new RegExp('^(?:' + source + ')', flags);
    }

    if (NPCG_INCLUDED) {
      reCopy = new RegExp('^' + source + '$(?!\\s)', flags);
    }
    if (UPDATES_LAST_INDEX_WRONG) lastIndex = re.lastIndex;

    match = call(nativeExec, sticky ? reCopy : re, strCopy);

    if (sticky) {
      if (match) {
        match.input = stringSlice(match.input, charsAdded);
        match[0] = stringSlice(match[0], charsAdded);
        match.index = re.lastIndex;
        re.lastIndex += match[0].length;
      } else re.lastIndex = 0;
    } else if (UPDATES_LAST_INDEX_WRONG && match) {
      re.lastIndex = re.global ? match.index + match[0].length : lastIndex;
    }
    if (NPCG_INCLUDED && match && match.length > 1) {
      // Fix browsers whose `exec` methods don't consistently return `undefined`
      // for NPCG, like IE8. NOTE: This doesn' work for /(.?)?/
      call(nativeReplace, match[0], reCopy, function () {
        for (i = 1; i < arguments.length - 2; i++) {
          if (arguments[i] === undefined) match[i] = undefined;
        }
      });
    }

    if (match && groups) {
      match.groups = object = create(null);
      for (i = 0; i < groups.length; i++) {
        group = groups[i];
        object[group[0]] = match[group[1]];
      }
    }

    return match;
  };
}

module.exports = patchedExec;


/***/ }),

/***/ 21182:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var anObject = __webpack_require__(34984);

// `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags
module.exports = function () {
  var that = anObject(this);
  var result = '';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.sticky) result += 'y';
  return result;
};


/***/ }),

/***/ 74846:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var global = __webpack_require__(32010);

// babel-minify and Closure Compiler transpiles RegExp('a', 'y') -> /a/y and it causes SyntaxError
var $RegExp = global.RegExp;

exports.UNSUPPORTED_Y = fails(function () {
  var re = $RegExp('a', 'y');
  re.lastIndex = 2;
  return re.exec('abcd') != null;
});

exports.BROKEN_CARET = fails(function () {
  // https://bugzilla.mozilla.org/show_bug.cgi?id=773687
  var re = $RegExp('^r', 'gy');
  re.lastIndex = 2;
  return re.exec('str') != null;
});


/***/ }),

/***/ 84030:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var global = __webpack_require__(32010);

// babel-minify and Closure Compiler transpiles RegExp('.', 's') -> /./s and it causes SyntaxError
var $RegExp = global.RegExp;

module.exports = fails(function () {
  var re = $RegExp('.', 's');
  return !(re.dotAll && re.exec('\n') && re.flags === 's');
});


/***/ }),

/***/ 97739:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);
var global = __webpack_require__(32010);

// babel-minify and Closure Compiler transpiles RegExp('(?<a>b)', 'g') -> /(?<a>b)/g and it causes SyntaxError
var $RegExp = global.RegExp;

module.exports = fails(function () {
  var re = $RegExp('(?<a>b)', 'g');
  return re.exec('b').groups.a !== 'b' ||
    'b'.replace(re, '$<a>c') !== 'bc';
});


/***/ }),

/***/ 83943:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

var TypeError = global.TypeError;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
module.exports = function (it) {
  if (it == undefined) throw TypeError("Can't call method on " + it);
  return it;
};


/***/ }),

/***/ 7421:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

// eslint-disable-next-line es/no-object-defineproperty -- safe
var defineProperty = Object.defineProperty;

module.exports = function (key, value) {
  try {
    defineProperty(global, key, { value: value, configurable: true, writable: true });
  } catch (error) {
    global[key] = value;
  } return value;
};


/***/ }),

/***/ 51334:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var getBuiltIn = __webpack_require__(38486);
var definePropertyModule = __webpack_require__(95892);
var wellKnownSymbol = __webpack_require__(38688);
var DESCRIPTORS = __webpack_require__(15567);

var SPECIES = wellKnownSymbol('species');

module.exports = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn(CONSTRUCTOR_NAME);
  var defineProperty = definePropertyModule.f;

  if (DESCRIPTORS && Constructor && !Constructor[SPECIES]) {
    defineProperty(Constructor, SPECIES, {
      configurable: true,
      get: function () { return this; }
    });
  }
};


/***/ }),

/***/ 15216:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var defineProperty = (__webpack_require__(95892).f);
var hasOwn = __webpack_require__(20340);
var wellKnownSymbol = __webpack_require__(38688);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');

module.exports = function (it, TAG, STATIC) {
  if (it && !hasOwn(it = STATIC ? it : it.prototype, TO_STRING_TAG)) {
    defineProperty(it, TO_STRING_TAG, { configurable: true, value: TAG });
  }
};


/***/ }),

/***/ 82194:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var shared = __webpack_require__(464);
var uid = __webpack_require__(46859);

var keys = shared('keys');

module.exports = function (key) {
  return keys[key] || (keys[key] = uid(key));
};


/***/ }),

/***/ 55480:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var setGlobal = __webpack_require__(7421);

var SHARED = '__core-js_shared__';
var store = global[SHARED] || setGlobal(SHARED, {});

module.exports = store;


/***/ }),

/***/ 464:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var IS_PURE = __webpack_require__(63432);
var store = __webpack_require__(55480);

(module.exports = function (key, value) {
  return store[key] || (store[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.19.0',
  mode: IS_PURE ? 'pure' : 'global',
  copyright: '© 2021 Denis Pushkarev (zloirock.ru)'
});


/***/ }),

/***/ 27754:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var anObject = __webpack_require__(34984);
var aConstructor = __webpack_require__(69075);
var wellKnownSymbol = __webpack_require__(38688);

var SPECIES = wellKnownSymbol('species');

// `SpeciesConstructor` abstract operation
// https://tc39.es/ecma262/#sec-speciesconstructor
module.exports = function (O, defaultConstructor) {
  var C = anObject(O).constructor;
  var S;
  return C === undefined || (S = anObject(C)[SPECIES]) == undefined ? defaultConstructor : aConstructor(S);
};


/***/ }),

/***/ 7452:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var fails = __webpack_require__(47044);

// check the existence of a method, lowercase
// of a tag and escaping quotes in arguments
module.exports = function (METHOD_NAME) {
  return fails(function () {
    var test = ''[METHOD_NAME]('"');
    return test !== test.toLowerCase() || test.split('"').length > 3;
  });
};


/***/ }),

/***/ 69510:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var toIntegerOrInfinity = __webpack_require__(26882);
var toString = __webpack_require__(25096);
var requireObjectCoercible = __webpack_require__(83943);

var charAt = uncurryThis(''.charAt);
var charCodeAt = uncurryThis(''.charCodeAt);
var stringSlice = uncurryThis(''.slice);

var createMethod = function (CONVERT_TO_STRING) {
  return function ($this, pos) {
    var S = toString(requireObjectCoercible($this));
    var position = toIntegerOrInfinity(pos);
    var size = S.length;
    var first, second;
    if (position < 0 || position >= size) return CONVERT_TO_STRING ? '' : undefined;
    first = charCodeAt(S, position);
    return first < 0xD800 || first > 0xDBFF || position + 1 === size
      || (second = charCodeAt(S, position + 1)) < 0xDC00 || second > 0xDFFF
        ? CONVERT_TO_STRING
          ? charAt(S, position)
          : first
        : CONVERT_TO_STRING
          ? stringSlice(S, position, position + 2)
          : (first - 0xD800 << 10) + (second - 0xDC00) + 0x10000;
  };
};

module.exports = {
  // `String.prototype.codePointAt` method
  // https://tc39.es/ecma262/#sec-string.prototype.codepointat
  codeAt: createMethod(false),
  // `String.prototype.at` method
  // https://github.com/mathiasbynens/String.prototype.at
  charAt: createMethod(true)
};


/***/ }),

/***/ 34858:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var toIntegerOrInfinity = __webpack_require__(26882);
var toString = __webpack_require__(25096);
var requireObjectCoercible = __webpack_require__(83943);

var RangeError = global.RangeError;

// `String.prototype.repeat` method implementation
// https://tc39.es/ecma262/#sec-string.prototype.repeat
module.exports = function repeat(count) {
  var str = toString(requireObjectCoercible(this));
  var result = '';
  var n = toIntegerOrInfinity(count);
  if (n < 0 || n == Infinity) throw RangeError('Wrong number of repetitions');
  for (;n > 0; (n >>>= 1) && (str += str)) if (n & 1) result += str;
  return result;
};


/***/ }),

/***/ 68899:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var PROPER_FUNCTION_NAME = (__webpack_require__(7081).PROPER);
var fails = __webpack_require__(47044);
var whitespaces = __webpack_require__(43187);

var non = '\u200B\u0085\u180E';

// check that a method works with the correct list
// of whitespaces and has a correct name
module.exports = function (METHOD_NAME) {
  return fails(function () {
    return !!whitespaces[METHOD_NAME]()
      || non[METHOD_NAME]() !== non
      || (PROPER_FUNCTION_NAME && whitespaces[METHOD_NAME].name !== METHOD_NAME);
  });
};


/***/ }),

/***/ 29841:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);
var requireObjectCoercible = __webpack_require__(83943);
var toString = __webpack_require__(25096);
var whitespaces = __webpack_require__(43187);

var replace = uncurryThis(''.replace);
var whitespace = '[' + whitespaces + ']';
var ltrim = RegExp('^' + whitespace + whitespace + '*');
var rtrim = RegExp(whitespace + whitespace + '*$');

// `String.prototype.{ trim, trimStart, trimEnd, trimLeft, trimRight }` methods implementation
var createMethod = function (TYPE) {
  return function ($this) {
    var string = toString(requireObjectCoercible($this));
    if (TYPE & 1) string = replace(string, ltrim, '');
    if (TYPE & 2) string = replace(string, rtrim, '');
    return string;
  };
};

module.exports = {
  // `String.prototype.{ trimLeft, trimStart }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimstart
  start: createMethod(1),
  // `String.prototype.{ trimRight, trimEnd }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimend
  end: createMethod(2),
  // `String.prototype.trim` method
  // https://tc39.es/ecma262/#sec-string.prototype.trim
  trim: createMethod(3)
};


/***/ }),

/***/ 6616:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var apply = __webpack_require__(58448);
var bind = __webpack_require__(25567);
var isCallable = __webpack_require__(94578);
var hasOwn = __webpack_require__(20340);
var fails = __webpack_require__(47044);
var html = __webpack_require__(520);
var arraySlice = __webpack_require__(73163);
var createElement = __webpack_require__(12072);
var IS_IOS = __webpack_require__(17716);
var IS_NODE = __webpack_require__(95053);

var set = global.setImmediate;
var clear = global.clearImmediate;
var process = global.process;
var Dispatch = global.Dispatch;
var Function = global.Function;
var MessageChannel = global.MessageChannel;
var String = global.String;
var counter = 0;
var queue = {};
var ONREADYSTATECHANGE = 'onreadystatechange';
var location, defer, channel, port;

try {
  // Deno throws a ReferenceError on `location` access without `--location` flag
  location = global.location;
} catch (error) { /* empty */ }

var run = function (id) {
  if (hasOwn(queue, id)) {
    var fn = queue[id];
    delete queue[id];
    fn();
  }
};

var runner = function (id) {
  return function () {
    run(id);
  };
};

var listener = function (event) {
  run(event.data);
};

var post = function (id) {
  // old engines have not location.origin
  global.postMessage(String(id), location.protocol + '//' + location.host);
};

// Node.js 0.9+ & IE10+ has setImmediate, otherwise:
if (!set || !clear) {
  set = function setImmediate(fn) {
    var args = arraySlice(arguments, 1);
    queue[++counter] = function () {
      apply(isCallable(fn) ? fn : Function(fn), undefined, args);
    };
    defer(counter);
    return counter;
  };
  clear = function clearImmediate(id) {
    delete queue[id];
  };
  // Node.js 0.8-
  if (IS_NODE) {
    defer = function (id) {
      process.nextTick(runner(id));
    };
  // Sphere (JS game engine) Dispatch API
  } else if (Dispatch && Dispatch.now) {
    defer = function (id) {
      Dispatch.now(runner(id));
    };
  // Browsers with MessageChannel, includes WebWorkers
  // except iOS - https://github.com/zloirock/core-js/issues/624
  } else if (MessageChannel && !IS_IOS) {
    channel = new MessageChannel();
    port = channel.port2;
    channel.port1.onmessage = listener;
    defer = bind(port.postMessage, port);
  // Browsers with postMessage, skip WebWorkers
  // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (
    global.addEventListener &&
    isCallable(global.postMessage) &&
    !global.importScripts &&
    location && location.protocol !== 'file:' &&
    !fails(post)
  ) {
    defer = post;
    global.addEventListener('message', listener, false);
  // IE8-
  } else if (ONREADYSTATECHANGE in createElement('script')) {
    defer = function (id) {
      html.appendChild(createElement('script'))[ONREADYSTATECHANGE] = function () {
        html.removeChild(this);
        run(id);
      };
    };
  // Rest old browsers
  } else {
    defer = function (id) {
      setTimeout(runner(id), 0);
    };
  }
}

module.exports = {
  set: set,
  clear: clear
};


/***/ }),

/***/ 16679:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);

// `thisNumberValue` abstract operation
// https://tc39.es/ecma262/#sec-thisnumbervalue
module.exports = uncurryThis(1.0.valueOf);


/***/ }),

/***/ 74841:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toIntegerOrInfinity = __webpack_require__(26882);

var max = Math.max;
var min = Math.min;

// Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).
module.exports = function (index, length) {
  var integer = toIntegerOrInfinity(index);
  return integer < 0 ? max(integer + length, 0) : min(integer, length);
};


/***/ }),

/***/ 71265:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var toIntegerOrInfinity = __webpack_require__(26882);
var toLength = __webpack_require__(23417);

var RangeError = global.RangeError;

// `ToIndex` abstract operation
// https://tc39.es/ecma262/#sec-toindex
module.exports = function (it) {
  if (it === undefined) return 0;
  var number = toIntegerOrInfinity(it);
  var length = toLength(number);
  if (number !== length) throw RangeError('Wrong length or index');
  return length;
};


/***/ }),

/***/ 98086:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

// toObject with fallback for non-array-like ES3 strings
var IndexedObject = __webpack_require__(7514);
var requireObjectCoercible = __webpack_require__(83943);

module.exports = function (it) {
  return IndexedObject(requireObjectCoercible(it));
};


/***/ }),

/***/ 26882:
/***/ (function(module) {

var ceil = Math.ceil;
var floor = Math.floor;

// `ToIntegerOrInfinity` abstract operation
// https://tc39.es/ecma262/#sec-tointegerorinfinity
module.exports = function (argument) {
  var number = +argument;
  // eslint-disable-next-line no-self-compare -- safe
  return number !== number || number === 0 ? 0 : (number > 0 ? floor : ceil)(number);
};


/***/ }),

/***/ 23417:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toIntegerOrInfinity = __webpack_require__(26882);

var min = Math.min;

// `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength
module.exports = function (argument) {
  return argument > 0 ? min(toIntegerOrInfinity(argument), 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};


/***/ }),

/***/ 43162:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var requireObjectCoercible = __webpack_require__(83943);

var Object = global.Object;

// `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject
module.exports = function (argument) {
  return Object(requireObjectCoercible(argument));
};


/***/ }),

/***/ 80670:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var toPositiveInteger = __webpack_require__(64913);

var RangeError = global.RangeError;

module.exports = function (it, BYTES) {
  var offset = toPositiveInteger(it);
  if (offset % BYTES) throw RangeError('Wrong offset');
  return offset;
};


/***/ }),

/***/ 64913:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var toIntegerOrInfinity = __webpack_require__(26882);

var RangeError = global.RangeError;

module.exports = function (it) {
  var result = toIntegerOrInfinity(it);
  if (result < 0) throw RangeError("The argument can't be less than 0");
  return result;
};


/***/ }),

/***/ 2644:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var isObject = __webpack_require__(24517);
var isSymbol = __webpack_require__(46290);
var getMethod = __webpack_require__(51839);
var ordinaryToPrimitive = __webpack_require__(39629);
var wellKnownSymbol = __webpack_require__(38688);

var TypeError = global.TypeError;
var TO_PRIMITIVE = wellKnownSymbol('toPrimitive');

// `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
module.exports = function (input, pref) {
  if (!isObject(input) || isSymbol(input)) return input;
  var exoticToPrim = getMethod(input, TO_PRIMITIVE);
  var result;
  if (exoticToPrim) {
    if (pref === undefined) pref = 'default';
    result = call(exoticToPrim, input, pref);
    if (!isObject(result) || isSymbol(result)) return result;
    throw TypeError("Can't convert object to primitive value");
  }
  if (pref === undefined) pref = 'number';
  return ordinaryToPrimitive(input, pref);
};


/***/ }),

/***/ 63918:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var toPrimitive = __webpack_require__(2644);
var isSymbol = __webpack_require__(46290);

// `ToPropertyKey` abstract operation
// https://tc39.es/ecma262/#sec-topropertykey
module.exports = function (argument) {
  var key = toPrimitive(argument, 'string');
  return isSymbol(key) ? key : key + '';
};


/***/ }),

/***/ 24663:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);

var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var test = {};

test[TO_STRING_TAG] = 'z';

module.exports = String(test) === '[object z]';


/***/ }),

/***/ 25096:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var classof = __webpack_require__(52564);

var String = global.String;

module.exports = function (argument) {
  if (classof(argument) === 'Symbol') throw TypeError('Cannot convert a Symbol value to a string');
  return String(argument);
};


/***/ }),

/***/ 68664:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);

var String = global.String;

module.exports = function (argument) {
  try {
    return String(argument);
  } catch (error) {
    return 'Object';
  }
};


/***/ }),

/***/ 98828:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var DESCRIPTORS = __webpack_require__(15567);
var TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS = __webpack_require__(28834);
var ArrayBufferViewCore = __webpack_require__(59754);
var ArrayBufferModule = __webpack_require__(83124);
var anInstance = __webpack_require__(2868);
var createPropertyDescriptor = __webpack_require__(97841);
var createNonEnumerableProperty = __webpack_require__(48914);
var isIntegralNumber = __webpack_require__(17506);
var toLength = __webpack_require__(23417);
var toIndex = __webpack_require__(71265);
var toOffset = __webpack_require__(80670);
var toPropertyKey = __webpack_require__(63918);
var hasOwn = __webpack_require__(20340);
var classof = __webpack_require__(52564);
var isObject = __webpack_require__(24517);
var isSymbol = __webpack_require__(46290);
var create = __webpack_require__(10819);
var isPrototypeOf = __webpack_require__(70176);
var setPrototypeOf = __webpack_require__(3840);
var getOwnPropertyNames = (__webpack_require__(6611).f);
var typedArrayFrom = __webpack_require__(83590);
var forEach = (__webpack_require__(91102).forEach);
var setSpecies = __webpack_require__(51334);
var definePropertyModule = __webpack_require__(95892);
var getOwnPropertyDescriptorModule = __webpack_require__(72062);
var InternalStateModule = __webpack_require__(70172);
var inheritIfRequired = __webpack_require__(51868);

var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var nativeDefineProperty = definePropertyModule.f;
var nativeGetOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
var round = Math.round;
var RangeError = global.RangeError;
var ArrayBuffer = ArrayBufferModule.ArrayBuffer;
var ArrayBufferPrototype = ArrayBuffer.prototype;
var DataView = ArrayBufferModule.DataView;
var NATIVE_ARRAY_BUFFER_VIEWS = ArrayBufferViewCore.NATIVE_ARRAY_BUFFER_VIEWS;
var TYPED_ARRAY_CONSTRUCTOR = ArrayBufferViewCore.TYPED_ARRAY_CONSTRUCTOR;
var TYPED_ARRAY_TAG = ArrayBufferViewCore.TYPED_ARRAY_TAG;
var TypedArray = ArrayBufferViewCore.TypedArray;
var TypedArrayPrototype = ArrayBufferViewCore.TypedArrayPrototype;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;
var isTypedArray = ArrayBufferViewCore.isTypedArray;
var BYTES_PER_ELEMENT = 'BYTES_PER_ELEMENT';
var WRONG_LENGTH = 'Wrong length';

var fromList = function (C, list) {
  aTypedArrayConstructor(C);
  var index = 0;
  var length = list.length;
  var result = new C(length);
  while (length > index) result[index] = list[index++];
  return result;
};

var addGetter = function (it, key) {
  nativeDefineProperty(it, key, { get: function () {
    return getInternalState(this)[key];
  } });
};

var isArrayBuffer = function (it) {
  var klass;
  return isPrototypeOf(ArrayBufferPrototype, it) || (klass = classof(it)) == 'ArrayBuffer' || klass == 'SharedArrayBuffer';
};

var isTypedArrayIndex = function (target, key) {
  return isTypedArray(target)
    && !isSymbol(key)
    && key in target
    && isIntegralNumber(+key)
    && key >= 0;
};

var wrappedGetOwnPropertyDescriptor = function getOwnPropertyDescriptor(target, key) {
  key = toPropertyKey(key);
  return isTypedArrayIndex(target, key)
    ? createPropertyDescriptor(2, target[key])
    : nativeGetOwnPropertyDescriptor(target, key);
};

var wrappedDefineProperty = function defineProperty(target, key, descriptor) {
  key = toPropertyKey(key);
  if (isTypedArrayIndex(target, key)
    && isObject(descriptor)
    && hasOwn(descriptor, 'value')
    && !hasOwn(descriptor, 'get')
    && !hasOwn(descriptor, 'set')
    // TODO: add validation descriptor w/o calling accessors
    && !descriptor.configurable
    && (!hasOwn(descriptor, 'writable') || descriptor.writable)
    && (!hasOwn(descriptor, 'enumerable') || descriptor.enumerable)
  ) {
    target[key] = descriptor.value;
    return target;
  } return nativeDefineProperty(target, key, descriptor);
};

if (DESCRIPTORS) {
  if (!NATIVE_ARRAY_BUFFER_VIEWS) {
    getOwnPropertyDescriptorModule.f = wrappedGetOwnPropertyDescriptor;
    definePropertyModule.f = wrappedDefineProperty;
    addGetter(TypedArrayPrototype, 'buffer');
    addGetter(TypedArrayPrototype, 'byteOffset');
    addGetter(TypedArrayPrototype, 'byteLength');
    addGetter(TypedArrayPrototype, 'length');
  }

  $({ target: 'Object', stat: true, forced: !NATIVE_ARRAY_BUFFER_VIEWS }, {
    getOwnPropertyDescriptor: wrappedGetOwnPropertyDescriptor,
    defineProperty: wrappedDefineProperty
  });

  module.exports = function (TYPE, wrapper, CLAMPED) {
    var BYTES = TYPE.match(/\d+$/)[0] / 8;
    var CONSTRUCTOR_NAME = TYPE + (CLAMPED ? 'Clamped' : '') + 'Array';
    var GETTER = 'get' + TYPE;
    var SETTER = 'set' + TYPE;
    var NativeTypedArrayConstructor = global[CONSTRUCTOR_NAME];
    var TypedArrayConstructor = NativeTypedArrayConstructor;
    var TypedArrayConstructorPrototype = TypedArrayConstructor && TypedArrayConstructor.prototype;
    var exported = {};

    var getter = function (that, index) {
      var data = getInternalState(that);
      return data.view[GETTER](index * BYTES + data.byteOffset, true);
    };

    var setter = function (that, index, value) {
      var data = getInternalState(that);
      if (CLAMPED) value = (value = round(value)) < 0 ? 0 : value > 0xFF ? 0xFF : value & 0xFF;
      data.view[SETTER](index * BYTES + data.byteOffset, value, true);
    };

    var addElement = function (that, index) {
      nativeDefineProperty(that, index, {
        get: function () {
          return getter(this, index);
        },
        set: function (value) {
          return setter(this, index, value);
        },
        enumerable: true
      });
    };

    if (!NATIVE_ARRAY_BUFFER_VIEWS) {
      TypedArrayConstructor = wrapper(function (that, data, offset, $length) {
        anInstance(that, TypedArrayConstructorPrototype);
        var index = 0;
        var byteOffset = 0;
        var buffer, byteLength, length;
        if (!isObject(data)) {
          length = toIndex(data);
          byteLength = length * BYTES;
          buffer = new ArrayBuffer(byteLength);
        } else if (isArrayBuffer(data)) {
          buffer = data;
          byteOffset = toOffset(offset, BYTES);
          var $len = data.byteLength;
          if ($length === undefined) {
            if ($len % BYTES) throw RangeError(WRONG_LENGTH);
            byteLength = $len - byteOffset;
            if (byteLength < 0) throw RangeError(WRONG_LENGTH);
          } else {
            byteLength = toLength($length) * BYTES;
            if (byteLength + byteOffset > $len) throw RangeError(WRONG_LENGTH);
          }
          length = byteLength / BYTES;
        } else if (isTypedArray(data)) {
          return fromList(TypedArrayConstructor, data);
        } else {
          return call(typedArrayFrom, TypedArrayConstructor, data);
        }
        setInternalState(that, {
          buffer: buffer,
          byteOffset: byteOffset,
          byteLength: byteLength,
          length: length,
          view: new DataView(buffer)
        });
        while (index < length) addElement(that, index++);
      });

      if (setPrototypeOf) setPrototypeOf(TypedArrayConstructor, TypedArray);
      TypedArrayConstructorPrototype = TypedArrayConstructor.prototype = create(TypedArrayPrototype);
    } else if (TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS) {
      TypedArrayConstructor = wrapper(function (dummy, data, typedArrayOffset, $length) {
        anInstance(dummy, TypedArrayConstructorPrototype);
        return inheritIfRequired(function () {
          if (!isObject(data)) return new NativeTypedArrayConstructor(toIndex(data));
          if (isArrayBuffer(data)) return $length !== undefined
            ? new NativeTypedArrayConstructor(data, toOffset(typedArrayOffset, BYTES), $length)
            : typedArrayOffset !== undefined
              ? new NativeTypedArrayConstructor(data, toOffset(typedArrayOffset, BYTES))
              : new NativeTypedArrayConstructor(data);
          if (isTypedArray(data)) return fromList(TypedArrayConstructor, data);
          return call(typedArrayFrom, TypedArrayConstructor, data);
        }(), dummy, TypedArrayConstructor);
      });

      if (setPrototypeOf) setPrototypeOf(TypedArrayConstructor, TypedArray);
      forEach(getOwnPropertyNames(NativeTypedArrayConstructor), function (key) {
        if (!(key in TypedArrayConstructor)) {
          createNonEnumerableProperty(TypedArrayConstructor, key, NativeTypedArrayConstructor[key]);
        }
      });
      TypedArrayConstructor.prototype = TypedArrayConstructorPrototype;
    }

    if (TypedArrayConstructorPrototype.constructor !== TypedArrayConstructor) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, 'constructor', TypedArrayConstructor);
    }

    createNonEnumerableProperty(TypedArrayConstructorPrototype, TYPED_ARRAY_CONSTRUCTOR, TypedArrayConstructor);

    if (TYPED_ARRAY_TAG) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, TYPED_ARRAY_TAG, CONSTRUCTOR_NAME);
    }

    exported[CONSTRUCTOR_NAME] = TypedArrayConstructor;

    $({
      global: true, forced: TypedArrayConstructor != NativeTypedArrayConstructor, sham: !NATIVE_ARRAY_BUFFER_VIEWS
    }, exported);

    if (!(BYTES_PER_ELEMENT in TypedArrayConstructor)) {
      createNonEnumerableProperty(TypedArrayConstructor, BYTES_PER_ELEMENT, BYTES);
    }

    if (!(BYTES_PER_ELEMENT in TypedArrayConstructorPrototype)) {
      createNonEnumerableProperty(TypedArrayConstructorPrototype, BYTES_PER_ELEMENT, BYTES);
    }

    setSpecies(CONSTRUCTOR_NAME);
  };
} else module.exports = function () { /* empty */ };


/***/ }),

/***/ 28834:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* eslint-disable no-new -- required for testing */
var global = __webpack_require__(32010);
var fails = __webpack_require__(47044);
var checkCorrectnessOfIteration = __webpack_require__(46769);
var NATIVE_ARRAY_BUFFER_VIEWS = (__webpack_require__(59754).NATIVE_ARRAY_BUFFER_VIEWS);

var ArrayBuffer = global.ArrayBuffer;
var Int8Array = global.Int8Array;

module.exports = !NATIVE_ARRAY_BUFFER_VIEWS || !fails(function () {
  Int8Array(1);
}) || !fails(function () {
  new Int8Array(-1);
}) || !checkCorrectnessOfIteration(function (iterable) {
  new Int8Array();
  new Int8Array(null);
  new Int8Array(1.5);
  new Int8Array(iterable);
}, true) || fails(function () {
  // Safari (11+) bug - a reason why even Safari 13 should load a typed array polyfill
  return new Int8Array(new ArrayBuffer(2), 1, undefined).length !== 1;
});


/***/ }),

/***/ 59610:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var arrayFromConstructorAndList = __webpack_require__(34269);
var typedArraySpeciesConstructor = __webpack_require__(34815);

module.exports = function (instance, list) {
  return arrayFromConstructorAndList(typedArraySpeciesConstructor(instance), list);
};


/***/ }),

/***/ 83590:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var bind = __webpack_require__(25567);
var call = __webpack_require__(2834);
var aConstructor = __webpack_require__(69075);
var toObject = __webpack_require__(43162);
var lengthOfArrayLike = __webpack_require__(45495);
var getIterator = __webpack_require__(15892);
var getIteratorMethod = __webpack_require__(13872);
var isArrayIteratorMethod = __webpack_require__(89564);
var aTypedArrayConstructor = (__webpack_require__(59754).aTypedArrayConstructor);

module.exports = function from(source /* , mapfn, thisArg */) {
  var C = aConstructor(this);
  var O = toObject(source);
  var argumentsLength = arguments.length;
  var mapfn = argumentsLength > 1 ? arguments[1] : undefined;
  var mapping = mapfn !== undefined;
  var iteratorMethod = getIteratorMethod(O);
  var i, length, result, step, iterator, next;
  if (iteratorMethod && !isArrayIteratorMethod(iteratorMethod)) {
    iterator = getIterator(O, iteratorMethod);
    next = iterator.next;
    O = [];
    while (!(step = call(next, iterator)).done) {
      O.push(step.value);
    }
  }
  if (mapping && argumentsLength > 2) {
    mapfn = bind(mapfn, arguments[2]);
  }
  length = lengthOfArrayLike(O);
  result = new (aTypedArrayConstructor(C))(length);
  for (i = 0; length > i; i++) {
    result[i] = mapping ? mapfn(O[i], i) : O[i];
  }
  return result;
};


/***/ }),

/***/ 34815:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var ArrayBufferViewCore = __webpack_require__(59754);
var speciesConstructor = __webpack_require__(27754);

var TYPED_ARRAY_CONSTRUCTOR = ArrayBufferViewCore.TYPED_ARRAY_CONSTRUCTOR;
var aTypedArrayConstructor = ArrayBufferViewCore.aTypedArrayConstructor;

// a part of `TypedArraySpeciesCreate` abstract operation
// https://tc39.es/ecma262/#typedarray-species-create
module.exports = function (originalArray) {
  return aTypedArrayConstructor(speciesConstructor(originalArray, originalArray[TYPED_ARRAY_CONSTRUCTOR]));
};


/***/ }),

/***/ 46859:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var uncurryThis = __webpack_require__(38347);

var id = 0;
var postfix = Math.random();
var toString = uncurryThis(1.0.toString);

module.exports = function (key) {
  return 'Symbol(' + (key === undefined ? '' : key) + ')_' + toString(++id + postfix, 36);
};


/***/ }),

/***/ 9567:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

/* eslint-disable es/no-symbol -- required for testing */
var NATIVE_SYMBOL = __webpack_require__(46887);

module.exports = NATIVE_SYMBOL
  && !Symbol.sham
  && typeof Symbol.iterator == 'symbol';


/***/ }),

/***/ 75960:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

var wellKnownSymbol = __webpack_require__(38688);

exports.f = wellKnownSymbol;


/***/ }),

/***/ 38688:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var shared = __webpack_require__(464);
var hasOwn = __webpack_require__(20340);
var uid = __webpack_require__(46859);
var NATIVE_SYMBOL = __webpack_require__(46887);
var USE_SYMBOL_AS_UID = __webpack_require__(9567);

var WellKnownSymbolsStore = shared('wks');
var Symbol = global.Symbol;
var symbolFor = Symbol && Symbol['for'];
var createWellKnownSymbol = USE_SYMBOL_AS_UID ? Symbol : Symbol && Symbol.withoutSetter || uid;

module.exports = function (name) {
  if (!hasOwn(WellKnownSymbolsStore, name) || !(NATIVE_SYMBOL || typeof WellKnownSymbolsStore[name] == 'string')) {
    var description = 'Symbol.' + name;
    if (NATIVE_SYMBOL && hasOwn(Symbol, name)) {
      WellKnownSymbolsStore[name] = Symbol[name];
    } else if (USE_SYMBOL_AS_UID && symbolFor) {
      WellKnownSymbolsStore[name] = symbolFor(description);
    } else {
      WellKnownSymbolsStore[name] = createWellKnownSymbol(description);
    }
  } return WellKnownSymbolsStore[name];
};


/***/ }),

/***/ 43187:
/***/ (function(module) {

// a string of all valid unicode whitespaces
module.exports = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u2000\u2001\u2002' +
  '\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';


/***/ }),

/***/ 94910:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var isPrototypeOf = __webpack_require__(70176);
var getPrototypeOf = __webpack_require__(69548);
var setPrototypeOf = __webpack_require__(3840);
var copyConstructorProperties = __webpack_require__(2675);
var create = __webpack_require__(10819);
var createNonEnumerableProperty = __webpack_require__(48914);
var createPropertyDescriptor = __webpack_require__(97841);
var clearErrorStack = __webpack_require__(34074);
var installErrorCause = __webpack_require__(87811);
var iterate = __webpack_require__(80383);
var normalizeStringArgument = __webpack_require__(86392);
var ERROR_STACK_INSTALLABLE = __webpack_require__(45144);

var Error = global.Error;
var push = [].push;

var $AggregateError = function AggregateError(errors, message /* , options */) {
  var that = isPrototypeOf(AggregateErrorPrototype, this) ? this : create(AggregateErrorPrototype);
  var options = arguments.length > 2 ? arguments[2] : undefined;
  if (setPrototypeOf) {
    that = setPrototypeOf(new Error(undefined), getPrototypeOf(that));
  }
  createNonEnumerableProperty(that, 'message', normalizeStringArgument(message, ''));
  if (ERROR_STACK_INSTALLABLE) createNonEnumerableProperty(that, 'stack', clearErrorStack(that.stack, 1));
  installErrorCause(that, options);
  var errorsArray = [];
  iterate(errors, push, { that: errorsArray });
  createNonEnumerableProperty(that, 'errors', errorsArray);
  return that;
};

if (setPrototypeOf) setPrototypeOf($AggregateError, Error);
else copyConstructorProperties($AggregateError, Error);

var AggregateErrorPrototype = $AggregateError.prototype = create(Error.prototype, {
  constructor: createPropertyDescriptor(1, $AggregateError),
  message: createPropertyDescriptor(1, ''),
  name: createPropertyDescriptor(1, 'AggregateError')
});

// `AggregateError` constructor
// https://tc39.es/ecma262/#sec-aggregate-error-constructor
$({ global: true }, {
  AggregateError: $AggregateError
});


/***/ }),

/***/ 39081:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var fails = __webpack_require__(47044);
var isArray = __webpack_require__(59113);
var isObject = __webpack_require__(24517);
var toObject = __webpack_require__(43162);
var lengthOfArrayLike = __webpack_require__(45495);
var createProperty = __webpack_require__(38639);
var arraySpeciesCreate = __webpack_require__(45744);
var arrayMethodHasSpeciesSupport = __webpack_require__(56280);
var wellKnownSymbol = __webpack_require__(38688);
var V8_VERSION = __webpack_require__(70091);

var IS_CONCAT_SPREADABLE = wellKnownSymbol('isConcatSpreadable');
var MAX_SAFE_INTEGER = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_INDEX_EXCEEDED = 'Maximum allowed index exceeded';
var TypeError = global.TypeError;

// We can't use this feature detection in V8 since it causes
// deoptimization and serious performance degradation
// https://github.com/zloirock/core-js/issues/679
var IS_CONCAT_SPREADABLE_SUPPORT = V8_VERSION >= 51 || !fails(function () {
  var array = [];
  array[IS_CONCAT_SPREADABLE] = false;
  return array.concat()[0] !== array;
});

var SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('concat');

var isConcatSpreadable = function (O) {
  if (!isObject(O)) return false;
  var spreadable = O[IS_CONCAT_SPREADABLE];
  return spreadable !== undefined ? !!spreadable : isArray(O);
};

var FORCED = !IS_CONCAT_SPREADABLE_SUPPORT || !SPECIES_SUPPORT;

// `Array.prototype.concat` method
// https://tc39.es/ecma262/#sec-array.prototype.concat
// with adding support of @@isConcatSpreadable and @@species
$({ target: 'Array', proto: true, forced: FORCED }, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  concat: function concat(arg) {
    var O = toObject(this);
    var A = arraySpeciesCreate(O, 0);
    var n = 0;
    var i, k, length, len, E;
    for (i = -1, length = arguments.length; i < length; i++) {
      E = i === -1 ? O : arguments[i];
      if (isConcatSpreadable(E)) {
        len = lengthOfArrayLike(E);
        if (n + len > MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);
        for (k = 0; k < len; k++, n++) if (k in E) createProperty(A, n, E[k]);
      } else {
        if (n >= MAX_SAFE_INTEGER) throw TypeError(MAXIMUM_ALLOWED_INDEX_EXCEEDED);
        createProperty(A, n++, E);
      }
    }
    A.length = n;
    return A;
  }
});


/***/ }),

/***/ 68626:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var copyWithin = __webpack_require__(92642);
var addToUnscopables = __webpack_require__(71156);

// `Array.prototype.copyWithin` method
// https://tc39.es/ecma262/#sec-array.prototype.copywithin
$({ target: 'Array', proto: true }, {
  copyWithin: copyWithin
});

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('copyWithin');


/***/ }),

/***/ 41584:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var fill = __webpack_require__(72864);
var addToUnscopables = __webpack_require__(71156);

// `Array.prototype.fill` method
// https://tc39.es/ecma262/#sec-array.prototype.fill
$({ target: 'Array', proto: true }, {
  fill: fill
});

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('fill');


/***/ }),

/***/ 49063:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var $filter = (__webpack_require__(91102).filter);
var arrayMethodHasSpeciesSupport = __webpack_require__(56280);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('filter');

// `Array.prototype.filter` method
// https://tc39.es/ecma262/#sec-array.prototype.filter
// with adding support of @@species
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT }, {
  filter: function filter(callbackfn /* , thisArg */) {
    return $filter(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),

/***/ 97514:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var $find = (__webpack_require__(91102).find);
var addToUnscopables = __webpack_require__(71156);

var FIND = 'find';
var SKIPS_HOLES = true;

// Shouldn't skip holes
if (FIND in []) Array(1)[FIND](function () { SKIPS_HOLES = false; });

// `Array.prototype.find` method
// https://tc39.es/ecma262/#sec-array.prototype.find
$({ target: 'Array', proto: true, forced: SKIPS_HOLES }, {
  find: function find(callbackfn /* , that = undefined */) {
    return $find(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables(FIND);


/***/ }),

/***/ 11765:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var from = __webpack_require__(95717);
var checkCorrectnessOfIteration = __webpack_require__(46769);

var INCORRECT_ITERATION = !checkCorrectnessOfIteration(function (iterable) {
  // eslint-disable-next-line es/no-array-from -- required for testing
  Array.from(iterable);
});

// `Array.from` method
// https://tc39.es/ecma262/#sec-array.from
$({ target: 'Array', stat: true, forced: INCORRECT_ITERATION }, {
  from: from
});


/***/ }),

/***/ 58028:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var $includes = (__webpack_require__(12636).includes);
var addToUnscopables = __webpack_require__(71156);

// `Array.prototype.includes` method
// https://tc39.es/ecma262/#sec-array.prototype.includes
$({ target: 'Array', proto: true }, {
  includes: function includes(el /* , fromIndex = 0 */) {
    return $includes(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
});

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('includes');


/***/ }),

/***/ 81755:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var toIndexedObject = __webpack_require__(98086);
var addToUnscopables = __webpack_require__(71156);
var Iterators = __webpack_require__(15366);
var InternalStateModule = __webpack_require__(70172);
var defineIterator = __webpack_require__(97001);

var ARRAY_ITERATOR = 'Array Iterator';
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(ARRAY_ITERATOR);

// `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator
module.exports = defineIterator(Array, 'Array', function (iterated, kind) {
  setInternalState(this, {
    type: ARRAY_ITERATOR,
    target: toIndexedObject(iterated), // target
    index: 0,                          // next index
    kind: kind                         // kind
  });
// `%ArrayIteratorPrototype%.next` method
// https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState(this);
  var target = state.target;
  var kind = state.kind;
  var index = state.index++;
  if (!target || index >= target.length) {
    state.target = undefined;
    return { value: undefined, done: true };
  }
  if (kind == 'keys') return { value: index, done: false };
  if (kind == 'values') return { value: target[index], done: false };
  return { value: [index, target[index]], done: false };
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject
Iterators.Arguments = Iterators.Array;

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('keys');
addToUnscopables('values');
addToUnscopables('entries');


/***/ }),

/***/ 94845:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var uncurryThis = __webpack_require__(38347);
var IndexedObject = __webpack_require__(7514);
var toIndexedObject = __webpack_require__(98086);
var arrayMethodIsStrict = __webpack_require__(81007);

var un$Join = uncurryThis([].join);

var ES3_STRINGS = IndexedObject != Object;
var STRICT_METHOD = arrayMethodIsStrict('join', ',');

// `Array.prototype.join` method
// https://tc39.es/ecma262/#sec-array.prototype.join
$({ target: 'Array', proto: true, forced: ES3_STRINGS || !STRICT_METHOD }, {
  join: function join(separator) {
    return un$Join(toIndexedObject(this), separator === undefined ? ',' : separator);
  }
});


/***/ }),

/***/ 80055:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var $map = (__webpack_require__(91102).map);
var arrayMethodHasSpeciesSupport = __webpack_require__(56280);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('map');

// `Array.prototype.map` method
// https://tc39.es/ecma262/#sec-array.prototype.map
// with adding support of @@species
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT }, {
  map: function map(callbackfn /* , thisArg */) {
    return $map(this, callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  }
});


/***/ }),

/***/ 20731:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var isArray = __webpack_require__(59113);
var isConstructor = __webpack_require__(20884);
var isObject = __webpack_require__(24517);
var toAbsoluteIndex = __webpack_require__(74841);
var lengthOfArrayLike = __webpack_require__(45495);
var toIndexedObject = __webpack_require__(98086);
var createProperty = __webpack_require__(38639);
var wellKnownSymbol = __webpack_require__(38688);
var arrayMethodHasSpeciesSupport = __webpack_require__(56280);
var un$Slice = __webpack_require__(73163);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('slice');

var SPECIES = wellKnownSymbol('species');
var Array = global.Array;
var max = Math.max;

// `Array.prototype.slice` method
// https://tc39.es/ecma262/#sec-array.prototype.slice
// fallback for not array-like ES3 strings and DOM objects
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT }, {
  slice: function slice(start, end) {
    var O = toIndexedObject(this);
    var length = lengthOfArrayLike(O);
    var k = toAbsoluteIndex(start, length);
    var fin = toAbsoluteIndex(end === undefined ? length : end, length);
    // inline `ArraySpeciesCreate` for usage native `Array#slice` where it's possible
    var Constructor, result, n;
    if (isArray(O)) {
      Constructor = O.constructor;
      // cross-realm fallback
      if (isConstructor(Constructor) && (Constructor === Array || isArray(Constructor.prototype))) {
        Constructor = undefined;
      } else if (isObject(Constructor)) {
        Constructor = Constructor[SPECIES];
        if (Constructor === null) Constructor = undefined;
      }
      if (Constructor === Array || Constructor === undefined) {
        return un$Slice(O, k, fin);
      }
    }
    result = new (Constructor === undefined ? Array : Constructor)(max(fin - k, 0));
    for (n = 0; k < fin; k++, n++) if (k in O) createProperty(result, n, O[k]);
    result.length = n;
    return result;
  }
});


/***/ }),

/***/ 45337:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var uncurryThis = __webpack_require__(38347);
var aCallable = __webpack_require__(32631);
var toObject = __webpack_require__(43162);
var lengthOfArrayLike = __webpack_require__(45495);
var toString = __webpack_require__(25096);
var fails = __webpack_require__(47044);
var internalSort = __webpack_require__(43977);
var arrayMethodIsStrict = __webpack_require__(81007);
var FF = __webpack_require__(3809);
var IE_OR_EDGE = __webpack_require__(21983);
var V8 = __webpack_require__(70091);
var WEBKIT = __webpack_require__(41731);

var test = [];
var un$Sort = uncurryThis(test.sort);
var push = uncurryThis(test.push);

// IE8-
var FAILS_ON_UNDEFINED = fails(function () {
  test.sort(undefined);
});
// V8 bug
var FAILS_ON_NULL = fails(function () {
  test.sort(null);
});
// Old WebKit
var STRICT_METHOD = arrayMethodIsStrict('sort');

var STABLE_SORT = !fails(function () {
  // feature detection can be too slow, so check engines versions
  if (V8) return V8 < 70;
  if (FF && FF > 3) return;
  if (IE_OR_EDGE) return true;
  if (WEBKIT) return WEBKIT < 603;

  var result = '';
  var code, chr, value, index;

  // generate an array with more 512 elements (Chakra and old V8 fails only in this case)
  for (code = 65; code < 76; code++) {
    chr = String.fromCharCode(code);

    switch (code) {
      case 66: case 69: case 70: case 72: value = 3; break;
      case 68: case 71: value = 4; break;
      default: value = 2;
    }

    for (index = 0; index < 47; index++) {
      test.push({ k: chr + index, v: value });
    }
  }

  test.sort(function (a, b) { return b.v - a.v; });

  for (index = 0; index < test.length; index++) {
    chr = test[index].k.charAt(0);
    if (result.charAt(result.length - 1) !== chr) result += chr;
  }

  return result !== 'DGBEFHACIJK';
});

var FORCED = FAILS_ON_UNDEFINED || !FAILS_ON_NULL || !STRICT_METHOD || !STABLE_SORT;

var getSortCompare = function (comparefn) {
  return function (x, y) {
    if (y === undefined) return -1;
    if (x === undefined) return 1;
    if (comparefn !== undefined) return +comparefn(x, y) || 0;
    return toString(x) > toString(y) ? 1 : -1;
  };
};

// `Array.prototype.sort` method
// https://tc39.es/ecma262/#sec-array.prototype.sort
$({ target: 'Array', proto: true, forced: FORCED }, {
  sort: function sort(comparefn) {
    if (comparefn !== undefined) aCallable(comparefn);

    var array = toObject(this);

    if (STABLE_SORT) return comparefn === undefined ? un$Sort(array) : un$Sort(array, comparefn);

    var items = [];
    var arrayLength = lengthOfArrayLike(array);
    var itemsLength, index;

    for (index = 0; index < arrayLength; index++) {
      if (index in array) push(items, array[index]);
    }

    internalSort(items, getSortCompare(comparefn));

    itemsLength = items.length;
    index = 0;

    while (index < itemsLength) array[index] = items[index++];
    while (index < arrayLength) delete array[index++];

    return array;
  }
});


/***/ }),

/***/ 23913:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var toAbsoluteIndex = __webpack_require__(74841);
var toIntegerOrInfinity = __webpack_require__(26882);
var lengthOfArrayLike = __webpack_require__(45495);
var toObject = __webpack_require__(43162);
var arraySpeciesCreate = __webpack_require__(45744);
var createProperty = __webpack_require__(38639);
var arrayMethodHasSpeciesSupport = __webpack_require__(56280);

var HAS_SPECIES_SUPPORT = arrayMethodHasSpeciesSupport('splice');

var TypeError = global.TypeError;
var max = Math.max;
var min = Math.min;
var MAX_SAFE_INTEGER = 0x1FFFFFFFFFFFFF;
var MAXIMUM_ALLOWED_LENGTH_EXCEEDED = 'Maximum allowed length exceeded';

// `Array.prototype.splice` method
// https://tc39.es/ecma262/#sec-array.prototype.splice
// with adding support of @@species
$({ target: 'Array', proto: true, forced: !HAS_SPECIES_SUPPORT }, {
  splice: function splice(start, deleteCount /* , ...items */) {
    var O = toObject(this);
    var len = lengthOfArrayLike(O);
    var actualStart = toAbsoluteIndex(start, len);
    var argumentsLength = arguments.length;
    var insertCount, actualDeleteCount, A, k, from, to;
    if (argumentsLength === 0) {
      insertCount = actualDeleteCount = 0;
    } else if (argumentsLength === 1) {
      insertCount = 0;
      actualDeleteCount = len - actualStart;
    } else {
      insertCount = argumentsLength - 2;
      actualDeleteCount = min(max(toIntegerOrInfinity(deleteCount), 0), len - actualStart);
    }
    if (len + insertCount - actualDeleteCount > MAX_SAFE_INTEGER) {
      throw TypeError(MAXIMUM_ALLOWED_LENGTH_EXCEEDED);
    }
    A = arraySpeciesCreate(O, actualDeleteCount);
    for (k = 0; k < actualDeleteCount; k++) {
      from = actualStart + k;
      if (from in O) createProperty(A, k, O[from]);
    }
    A.length = actualDeleteCount;
    if (insertCount < actualDeleteCount) {
      for (k = actualStart; k < len - actualDeleteCount; k++) {
        from = k + actualDeleteCount;
        to = k + insertCount;
        if (from in O) O[to] = O[from];
        else delete O[to];
      }
      for (k = len; k > len - actualDeleteCount + insertCount; k--) delete O[k - 1];
    } else if (insertCount > actualDeleteCount) {
      for (k = len - actualDeleteCount; k > actualStart; k--) {
        from = k + actualDeleteCount - 1;
        to = k + insertCount - 1;
        if (from in O) O[to] = O[from];
        else delete O[to];
      }
    }
    for (k = 0; k < insertCount; k++) {
      O[k + actualStart] = arguments[k + 2];
    }
    O.length = len - actualDeleteCount + insertCount;
    return A;
  }
});


/***/ }),

/***/ 7585:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var hasOwn = __webpack_require__(20340);
var redefine = __webpack_require__(13711);
var dateToPrimitive = __webpack_require__(53087);
var wellKnownSymbol = __webpack_require__(38688);

var TO_PRIMITIVE = wellKnownSymbol('toPrimitive');
var DatePrototype = Date.prototype;

// `Date.prototype[@@toPrimitive]` method
// https://tc39.es/ecma262/#sec-date.prototype-@@toprimitive
if (!hasOwn(DatePrototype, TO_PRIMITIVE)) {
  redefine(DatePrototype, TO_PRIMITIVE, dateToPrimitive);
}


/***/ }),

/***/ 24863:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var FUNCTION_NAME_EXISTS = (__webpack_require__(7081).EXISTS);
var uncurryThis = __webpack_require__(38347);
var defineProperty = (__webpack_require__(95892).f);

var FunctionPrototype = Function.prototype;
var functionToString = uncurryThis(FunctionPrototype.toString);
var nameRE = /^\s*function ([^ (]*)/;
var regExpExec = uncurryThis(nameRE.exec);
var NAME = 'name';

// Function instances `.name` property
// https://tc39.es/ecma262/#sec-function-instances-name
if (DESCRIPTORS && !FUNCTION_NAME_EXISTS) {
  defineProperty(FunctionPrototype, NAME, {
    configurable: true,
    get: function () {
      try {
        return regExpExec(nameRE, functionToString(this))[1];
      } catch (error) {
        return '';
      }
    }
  });
}


/***/ }),

/***/ 64384:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);

// `globalThis` object
// https://tc39.es/ecma262/#sec-globalthis
$({ global: true }, {
  globalThis: global
});


/***/ }),

/***/ 43448:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var setToStringTag = __webpack_require__(15216);

// JSON[@@toStringTag] property
// https://tc39.es/ecma262/#sec-json-@@tostringtag
setToStringTag(global.JSON, 'JSON', true);


/***/ }),

/***/ 18828:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var collection = __webpack_require__(36673);
var collectionStrong = __webpack_require__(9649);

// `Map` constructor
// https://tc39.es/ecma262/#sec-map-objects
collection('Map', function (init) {
  return function Map() { return init(this, arguments.length ? arguments[0] : undefined); };
}, collectionStrong);


/***/ }),

/***/ 63956:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var setToStringTag = __webpack_require__(15216);

// Math[@@toStringTag] property
// https://tc39.es/ecma262/#sec-math-@@tostringtag
setToStringTag(Math, 'Math', true);


/***/ }),

/***/ 7283:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var DESCRIPTORS = __webpack_require__(15567);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var isForced = __webpack_require__(39599);
var redefine = __webpack_require__(13711);
var hasOwn = __webpack_require__(20340);
var inheritIfRequired = __webpack_require__(51868);
var isPrototypeOf = __webpack_require__(70176);
var isSymbol = __webpack_require__(46290);
var toPrimitive = __webpack_require__(2644);
var fails = __webpack_require__(47044);
var getOwnPropertyNames = (__webpack_require__(6611).f);
var getOwnPropertyDescriptor = (__webpack_require__(72062).f);
var defineProperty = (__webpack_require__(95892).f);
var thisNumberValue = __webpack_require__(16679);
var trim = (__webpack_require__(29841).trim);

var NUMBER = 'Number';
var NativeNumber = global[NUMBER];
var NumberPrototype = NativeNumber.prototype;
var TypeError = global.TypeError;
var arraySlice = uncurryThis(''.slice);
var charCodeAt = uncurryThis(''.charCodeAt);

// `ToNumeric` abstract operation
// https://tc39.es/ecma262/#sec-tonumeric
var toNumeric = function (value) {
  var primValue = toPrimitive(value, 'number');
  return typeof primValue == 'bigint' ? primValue : toNumber(primValue);
};

// `ToNumber` abstract operation
// https://tc39.es/ecma262/#sec-tonumber
var toNumber = function (argument) {
  var it = toPrimitive(argument, 'number');
  var first, third, radix, maxCode, digits, length, index, code;
  if (isSymbol(it)) throw TypeError('Cannot convert a Symbol value to a number');
  if (typeof it == 'string' && it.length > 2) {
    it = trim(it);
    first = charCodeAt(it, 0);
    if (first === 43 || first === 45) {
      third = charCodeAt(it, 2);
      if (third === 88 || third === 120) return NaN; // Number('+0x1') should be NaN, old V8 fix
    } else if (first === 48) {
      switch (charCodeAt(it, 1)) {
        case 66: case 98: radix = 2; maxCode = 49; break; // fast equal of /^0b[01]+$/i
        case 79: case 111: radix = 8; maxCode = 55; break; // fast equal of /^0o[0-7]+$/i
        default: return +it;
      }
      digits = arraySlice(it, 2);
      length = digits.length;
      for (index = 0; index < length; index++) {
        code = charCodeAt(digits, index);
        // parseInt parses a string to a first unavailable symbol
        // but ToNumber should return NaN if a string contains unavailable symbols
        if (code < 48 || code > maxCode) return NaN;
      } return parseInt(digits, radix);
    }
  } return +it;
};

// `Number` constructor
// https://tc39.es/ecma262/#sec-number-constructor
if (isForced(NUMBER, !NativeNumber(' 0o1') || !NativeNumber('0b1') || NativeNumber('+0x1'))) {
  var NumberWrapper = function Number(value) {
    var n = arguments.length < 1 ? 0 : NativeNumber(toNumeric(value));
    var dummy = this;
    // check on 1..constructor(foo) case
    return isPrototypeOf(NumberPrototype, dummy) && fails(function () { thisNumberValue(dummy); })
      ? inheritIfRequired(Object(n), dummy, NumberWrapper) : n;
  };
  for (var keys = DESCRIPTORS ? getOwnPropertyNames(NativeNumber) : (
    // ES3:
    'MAX_VALUE,MIN_VALUE,NaN,NEGATIVE_INFINITY,POSITIVE_INFINITY,' +
    // ES2015 (in case, if modules with ES2015 Number statics required before):
    'EPSILON,MAX_SAFE_INTEGER,MIN_SAFE_INTEGER,isFinite,isInteger,isNaN,isSafeInteger,parseFloat,parseInt,' +
    // ESNext
    'fromString,range'
  ).split(','), j = 0, key; keys.length > j; j++) {
    if (hasOwn(NativeNumber, key = keys[j]) && !hasOwn(NumberWrapper, key)) {
      defineProperty(NumberWrapper, key, getOwnPropertyDescriptor(NativeNumber, key));
    }
  }
  NumberWrapper.prototype = NumberPrototype;
  NumberPrototype.constructor = NumberWrapper;
  redefine(global, NUMBER, NumberWrapper);
}


/***/ }),

/***/ 58549:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);

// `Number.EPSILON` constant
// https://tc39.es/ecma262/#sec-number.epsilon
$({ target: 'Number', stat: true }, {
  EPSILON: Math.pow(2, -52)
});


/***/ }),

/***/ 70095:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var numberIsFinite = __webpack_require__(59805);

// `Number.isFinite` method
// https://tc39.es/ecma262/#sec-number.isfinite
$({ target: 'Number', stat: true }, { isFinite: numberIsFinite });


/***/ }),

/***/ 2876:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var isIntegralNumber = __webpack_require__(17506);

// `Number.isInteger` method
// https://tc39.es/ecma262/#sec-number.isinteger
$({ target: 'Number', stat: true }, {
  isInteger: isIntegralNumber
});


/***/ }),

/***/ 10849:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var toIntegerOrInfinity = __webpack_require__(26882);
var thisNumberValue = __webpack_require__(16679);
var $repeat = __webpack_require__(34858);
var fails = __webpack_require__(47044);

var RangeError = global.RangeError;
var String = global.String;
var floor = Math.floor;
var repeat = uncurryThis($repeat);
var stringSlice = uncurryThis(''.slice);
var un$ToFixed = uncurryThis(1.0.toFixed);

var pow = function (x, n, acc) {
  return n === 0 ? acc : n % 2 === 1 ? pow(x, n - 1, acc * x) : pow(x * x, n / 2, acc);
};

var log = function (x) {
  var n = 0;
  var x2 = x;
  while (x2 >= 4096) {
    n += 12;
    x2 /= 4096;
  }
  while (x2 >= 2) {
    n += 1;
    x2 /= 2;
  } return n;
};

var multiply = function (data, n, c) {
  var index = -1;
  var c2 = c;
  while (++index < 6) {
    c2 += n * data[index];
    data[index] = c2 % 1e7;
    c2 = floor(c2 / 1e7);
  }
};

var divide = function (data, n) {
  var index = 6;
  var c = 0;
  while (--index >= 0) {
    c += data[index];
    data[index] = floor(c / n);
    c = (c % n) * 1e7;
  }
};

var dataToString = function (data) {
  var index = 6;
  var s = '';
  while (--index >= 0) {
    if (s !== '' || index === 0 || data[index] !== 0) {
      var t = String(data[index]);
      s = s === '' ? t : s + repeat('0', 7 - t.length) + t;
    }
  } return s;
};

var FORCED = fails(function () {
  return un$ToFixed(0.00008, 3) !== '0.000' ||
    un$ToFixed(0.9, 0) !== '1' ||
    un$ToFixed(1.255, 2) !== '1.25' ||
    un$ToFixed(1000000000000000128.0, 0) !== '1000000000000000128';
}) || !fails(function () {
  // V8 ~ Android 4.3-
  un$ToFixed({});
});

// `Number.prototype.toFixed` method
// https://tc39.es/ecma262/#sec-number.prototype.tofixed
$({ target: 'Number', proto: true, forced: FORCED }, {
  toFixed: function toFixed(fractionDigits) {
    var number = thisNumberValue(this);
    var fractDigits = toIntegerOrInfinity(fractionDigits);
    var data = [0, 0, 0, 0, 0, 0];
    var sign = '';
    var result = '0';
    var e, z, j, k;

    if (fractDigits < 0 || fractDigits > 20) throw RangeError('Incorrect fraction digits');
    // eslint-disable-next-line no-self-compare -- NaN check
    if (number != number) return 'NaN';
    if (number <= -1e21 || number >= 1e21) return String(number);
    if (number < 0) {
      sign = '-';
      number = -number;
    }
    if (number > 1e-21) {
      e = log(number * pow(2, 69, 1)) - 69;
      z = e < 0 ? number * pow(2, -e, 1) : number / pow(2, e, 1);
      z *= 0x10000000000000;
      e = 52 - e;
      if (e > 0) {
        multiply(data, 0, z);
        j = fractDigits;
        while (j >= 7) {
          multiply(data, 1e7, 0);
          j -= 7;
        }
        multiply(data, pow(10, j, 1), 0);
        j = e - 1;
        while (j >= 23) {
          divide(data, 1 << 23);
          j -= 23;
        }
        divide(data, 1 << j);
        multiply(data, 1, 1);
        divide(data, 2);
        result = dataToString(data);
      } else {
        multiply(data, 0, z);
        multiply(data, 1 << -e, 0);
        result = dataToString(data) + repeat('0', fractDigits);
      }
    }
    if (fractDigits > 0) {
      k = result.length;
      result = sign + (k <= fractDigits
        ? '0.' + repeat('0', fractDigits - k) + result
        : stringSlice(result, 0, k - fractDigits) + '.' + stringSlice(result, k - fractDigits));
    } else {
      result = sign + result;
    } return result;
  }
});


/***/ }),

/***/ 18756:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var assign = __webpack_require__(87146);

// `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
// eslint-disable-next-line es/no-object-assign -- required for testing
$({ target: 'Object', stat: true, forced: Object.assign !== assign }, {
  assign: assign
});


/***/ }),

/***/ 75174:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var FREEZING = __webpack_require__(55481);
var fails = __webpack_require__(47044);
var isObject = __webpack_require__(24517);
var onFreeze = (__webpack_require__(62148).onFreeze);

// eslint-disable-next-line es/no-object-freeze -- safe
var $freeze = Object.freeze;
var FAILS_ON_PRIMITIVES = fails(function () { $freeze(1); });

// `Object.freeze` method
// https://tc39.es/ecma262/#sec-object.freeze
$({ target: 'Object', stat: true, forced: FAILS_ON_PRIMITIVES, sham: !FREEZING }, {
  freeze: function freeze(it) {
    return $freeze && isObject(it) ? $freeze(onFreeze(it)) : it;
  }
});


/***/ }),

/***/ 57444:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var fails = __webpack_require__(47044);
var toIndexedObject = __webpack_require__(98086);
var nativeGetOwnPropertyDescriptor = (__webpack_require__(72062).f);
var DESCRIPTORS = __webpack_require__(15567);

var FAILS_ON_PRIMITIVES = fails(function () { nativeGetOwnPropertyDescriptor(1); });
var FORCED = !DESCRIPTORS || FAILS_ON_PRIMITIVES;

// `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor
$({ target: 'Object', stat: true, forced: FORCED, sham: !DESCRIPTORS }, {
  getOwnPropertyDescriptor: function getOwnPropertyDescriptor(it, key) {
    return nativeGetOwnPropertyDescriptor(toIndexedObject(it), key);
  }
});


/***/ }),

/***/ 28331:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var DESCRIPTORS = __webpack_require__(15567);
var ownKeys = __webpack_require__(21594);
var toIndexedObject = __webpack_require__(98086);
var getOwnPropertyDescriptorModule = __webpack_require__(72062);
var createProperty = __webpack_require__(38639);

// `Object.getOwnPropertyDescriptors` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptors
$({ target: 'Object', stat: true, sham: !DESCRIPTORS }, {
  getOwnPropertyDescriptors: function getOwnPropertyDescriptors(object) {
    var O = toIndexedObject(object);
    var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
    var keys = ownKeys(O);
    var result = {};
    var index = 0;
    var key, descriptor;
    while (keys.length > index) {
      descriptor = getOwnPropertyDescriptor(O, key = keys[index++]);
      if (descriptor !== undefined) createProperty(result, key, descriptor);
    }
    return result;
  }
});


/***/ }),

/***/ 71950:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var fails = __webpack_require__(47044);
var toObject = __webpack_require__(43162);
var nativeGetPrototypeOf = __webpack_require__(69548);
var CORRECT_PROTOTYPE_GETTER = __webpack_require__(68494);

var FAILS_ON_PRIMITIVES = fails(function () { nativeGetPrototypeOf(1); });

// `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
$({ target: 'Object', stat: true, forced: FAILS_ON_PRIMITIVES, sham: !CORRECT_PROTOTYPE_GETTER }, {
  getPrototypeOf: function getPrototypeOf(it) {
    return nativeGetPrototypeOf(toObject(it));
  }
});



/***/ }),

/***/ 37309:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var toObject = __webpack_require__(43162);
var nativeKeys = __webpack_require__(84675);
var fails = __webpack_require__(47044);

var FAILS_ON_PRIMITIVES = fails(function () { nativeKeys(1); });

// `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
$({ target: 'Object', stat: true, forced: FAILS_ON_PRIMITIVES }, {
  keys: function keys(it) {
    return nativeKeys(toObject(it));
  }
});


/***/ }),

/***/ 14032:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var TO_STRING_TAG_SUPPORT = __webpack_require__(24663);
var redefine = __webpack_require__(13711);
var toString = __webpack_require__(52598);

// `Object.prototype.toString` method
// https://tc39.es/ecma262/#sec-object.prototype.tostring
if (!TO_STRING_TAG_SUPPORT) {
  redefine(Object.prototype, 'toString', toString, { unsafe: true });
}


/***/ }),

/***/ 59883:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var $values = (__webpack_require__(80754).values);

// `Object.values` method
// https://tc39.es/ecma262/#sec-object.values
$({ target: 'Object', stat: true }, {
  values: function values(O) {
    return $values(O);
  }
});


/***/ }),

/***/ 77074:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var call = __webpack_require__(2834);
var aCallable = __webpack_require__(32631);
var newPromiseCapabilityModule = __webpack_require__(56614);
var perform = __webpack_require__(61900);
var iterate = __webpack_require__(80383);

// `Promise.allSettled` method
// https://tc39.es/ecma262/#sec-promise.allsettled
$({ target: 'Promise', stat: true }, {
  allSettled: function allSettled(iterable) {
    var C = this;
    var capability = newPromiseCapabilityModule.f(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var promiseResolve = aCallable(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        remaining++;
        call(promiseResolve, C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = { status: 'fulfilled', value: value };
          --remaining || resolve(values);
        }, function (error) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = { status: 'rejected', reason: error };
          --remaining || resolve(values);
        });
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),

/***/ 44455:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var aCallable = __webpack_require__(32631);
var getBuiltIn = __webpack_require__(38486);
var call = __webpack_require__(2834);
var newPromiseCapabilityModule = __webpack_require__(56614);
var perform = __webpack_require__(61900);
var iterate = __webpack_require__(80383);

var PROMISE_ANY_ERROR = 'No one promise resolved';

// `Promise.any` method
// https://tc39.es/ecma262/#sec-promise.any
$({ target: 'Promise', stat: true }, {
  any: function any(iterable) {
    var C = this;
    var AggregateError = getBuiltIn('AggregateError');
    var capability = newPromiseCapabilityModule.f(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var promiseResolve = aCallable(C.resolve);
      var errors = [];
      var counter = 0;
      var remaining = 1;
      var alreadyResolved = false;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyRejected = false;
        remaining++;
        call(promiseResolve, C, promise).then(function (value) {
          if (alreadyRejected || alreadyResolved) return;
          alreadyResolved = true;
          resolve(value);
        }, function (error) {
          if (alreadyRejected || alreadyResolved) return;
          alreadyRejected = true;
          errors[index] = error;
          --remaining || reject(new AggregateError(errors, PROMISE_ANY_ERROR));
        });
      });
      --remaining || reject(new AggregateError(errors, PROMISE_ANY_ERROR));
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),

/***/ 80986:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var IS_PURE = __webpack_require__(63432);
var NativePromise = __webpack_require__(5155);
var fails = __webpack_require__(47044);
var getBuiltIn = __webpack_require__(38486);
var isCallable = __webpack_require__(94578);
var speciesConstructor = __webpack_require__(27754);
var promiseResolve = __webpack_require__(28617);
var redefine = __webpack_require__(13711);

// Safari bug https://bugs.webkit.org/show_bug.cgi?id=200829
var NON_GENERIC = !!NativePromise && fails(function () {
  NativePromise.prototype['finally'].call({ then: function () { /* empty */ } }, function () { /* empty */ });
});

// `Promise.prototype.finally` method
// https://tc39.es/ecma262/#sec-promise.prototype.finally
$({ target: 'Promise', proto: true, real: true, forced: NON_GENERIC }, {
  'finally': function (onFinally) {
    var C = speciesConstructor(this, getBuiltIn('Promise'));
    var isFunction = isCallable(onFinally);
    return this.then(
      isFunction ? function (x) {
        return promiseResolve(C, onFinally()).then(function () { return x; });
      } : onFinally,
      isFunction ? function (e) {
        return promiseResolve(C, onFinally()).then(function () { throw e; });
      } : onFinally
    );
  }
});

// makes sure that native promise-based APIs `Promise#finally` properly works with patched `Promise#then`
if (!IS_PURE && isCallable(NativePromise)) {
  var method = getBuiltIn('Promise').prototype['finally'];
  if (NativePromise.prototype['finally'] !== method) {
    redefine(NativePromise.prototype, 'finally', method, { unsafe: true });
  }
}


/***/ }),

/***/ 68067:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var IS_PURE = __webpack_require__(63432);
var global = __webpack_require__(32010);
var getBuiltIn = __webpack_require__(38486);
var call = __webpack_require__(2834);
var NativePromise = __webpack_require__(5155);
var redefine = __webpack_require__(13711);
var redefineAll = __webpack_require__(15341);
var setPrototypeOf = __webpack_require__(3840);
var setToStringTag = __webpack_require__(15216);
var setSpecies = __webpack_require__(51334);
var aCallable = __webpack_require__(32631);
var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);
var anInstance = __webpack_require__(2868);
var inspectSource = __webpack_require__(10447);
var iterate = __webpack_require__(80383);
var checkCorrectnessOfIteration = __webpack_require__(46769);
var speciesConstructor = __webpack_require__(27754);
var task = (__webpack_require__(6616).set);
var microtask = __webpack_require__(59804);
var promiseResolve = __webpack_require__(28617);
var hostReportErrors = __webpack_require__(61144);
var newPromiseCapabilityModule = __webpack_require__(56614);
var perform = __webpack_require__(61900);
var InternalStateModule = __webpack_require__(70172);
var isForced = __webpack_require__(39599);
var wellKnownSymbol = __webpack_require__(38688);
var IS_BROWSER = __webpack_require__(3157);
var IS_NODE = __webpack_require__(95053);
var V8_VERSION = __webpack_require__(70091);

var SPECIES = wellKnownSymbol('species');
var PROMISE = 'Promise';

var getInternalState = InternalStateModule.get;
var setInternalState = InternalStateModule.set;
var getInternalPromiseState = InternalStateModule.getterFor(PROMISE);
var NativePromisePrototype = NativePromise && NativePromise.prototype;
var PromiseConstructor = NativePromise;
var PromisePrototype = NativePromisePrototype;
var TypeError = global.TypeError;
var document = global.document;
var process = global.process;
var newPromiseCapability = newPromiseCapabilityModule.f;
var newGenericPromiseCapability = newPromiseCapability;

var DISPATCH_EVENT = !!(document && document.createEvent && global.dispatchEvent);
var NATIVE_REJECTION_EVENT = isCallable(global.PromiseRejectionEvent);
var UNHANDLED_REJECTION = 'unhandledrejection';
var REJECTION_HANDLED = 'rejectionhandled';
var PENDING = 0;
var FULFILLED = 1;
var REJECTED = 2;
var HANDLED = 1;
var UNHANDLED = 2;
var SUBCLASSING = false;

var Internal, OwnPromiseCapability, PromiseWrapper, nativeThen;

var FORCED = isForced(PROMISE, function () {
  var PROMISE_CONSTRUCTOR_SOURCE = inspectSource(PromiseConstructor);
  var GLOBAL_CORE_JS_PROMISE = PROMISE_CONSTRUCTOR_SOURCE !== String(PromiseConstructor);
  // V8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
  // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
  // We can't detect it synchronously, so just check versions
  if (!GLOBAL_CORE_JS_PROMISE && V8_VERSION === 66) return true;
  // We need Promise#finally in the pure version for preventing prototype pollution
  if (IS_PURE && !PromisePrototype['finally']) return true;
  // We can't use @@species feature detection in V8 since it causes
  // deoptimization and performance degradation
  // https://github.com/zloirock/core-js/issues/679
  if (V8_VERSION >= 51 && /native code/.test(PROMISE_CONSTRUCTOR_SOURCE)) return false;
  // Detect correctness of subclassing with @@species support
  var promise = new PromiseConstructor(function (resolve) { resolve(1); });
  var FakePromise = function (exec) {
    exec(function () { /* empty */ }, function () { /* empty */ });
  };
  var constructor = promise.constructor = {};
  constructor[SPECIES] = FakePromise;
  SUBCLASSING = promise.then(function () { /* empty */ }) instanceof FakePromise;
  if (!SUBCLASSING) return true;
  // Unhandled rejections tracking support, NodeJS Promise without it fails @@species test
  return !GLOBAL_CORE_JS_PROMISE && IS_BROWSER && !NATIVE_REJECTION_EVENT;
});

var INCORRECT_ITERATION = FORCED || !checkCorrectnessOfIteration(function (iterable) {
  PromiseConstructor.all(iterable)['catch'](function () { /* empty */ });
});

// helpers
var isThenable = function (it) {
  var then;
  return isObject(it) && isCallable(then = it.then) ? then : false;
};

var notify = function (state, isReject) {
  if (state.notified) return;
  state.notified = true;
  var chain = state.reactions;
  microtask(function () {
    var value = state.value;
    var ok = state.state == FULFILLED;
    var index = 0;
    // variable length - can't use forEach
    while (chain.length > index) {
      var reaction = chain[index++];
      var handler = ok ? reaction.ok : reaction.fail;
      var resolve = reaction.resolve;
      var reject = reaction.reject;
      var domain = reaction.domain;
      var result, then, exited;
      try {
        if (handler) {
          if (!ok) {
            if (state.rejection === UNHANDLED) onHandleUnhandled(state);
            state.rejection = HANDLED;
          }
          if (handler === true) result = value;
          else {
            if (domain) domain.enter();
            result = handler(value); // can throw
            if (domain) {
              domain.exit();
              exited = true;
            }
          }
          if (result === reaction.promise) {
            reject(TypeError('Promise-chain cycle'));
          } else if (then = isThenable(result)) {
            call(then, result, resolve, reject);
          } else resolve(result);
        } else reject(value);
      } catch (error) {
        if (domain && !exited) domain.exit();
        reject(error);
      }
    }
    state.reactions = [];
    state.notified = false;
    if (isReject && !state.rejection) onUnhandled(state);
  });
};

var dispatchEvent = function (name, promise, reason) {
  var event, handler;
  if (DISPATCH_EVENT) {
    event = document.createEvent('Event');
    event.promise = promise;
    event.reason = reason;
    event.initEvent(name, false, true);
    global.dispatchEvent(event);
  } else event = { promise: promise, reason: reason };
  if (!NATIVE_REJECTION_EVENT && (handler = global['on' + name])) handler(event);
  else if (name === UNHANDLED_REJECTION) hostReportErrors('Unhandled promise rejection', reason);
};

var onUnhandled = function (state) {
  call(task, global, function () {
    var promise = state.facade;
    var value = state.value;
    var IS_UNHANDLED = isUnhandled(state);
    var result;
    if (IS_UNHANDLED) {
      result = perform(function () {
        if (IS_NODE) {
          process.emit('unhandledRejection', value, promise);
        } else dispatchEvent(UNHANDLED_REJECTION, promise, value);
      });
      // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should
      state.rejection = IS_NODE || isUnhandled(state) ? UNHANDLED : HANDLED;
      if (result.error) throw result.value;
    }
  });
};

var isUnhandled = function (state) {
  return state.rejection !== HANDLED && !state.parent;
};

var onHandleUnhandled = function (state) {
  call(task, global, function () {
    var promise = state.facade;
    if (IS_NODE) {
      process.emit('rejectionHandled', promise);
    } else dispatchEvent(REJECTION_HANDLED, promise, state.value);
  });
};

var bind = function (fn, state, unwrap) {
  return function (value) {
    fn(state, value, unwrap);
  };
};

var internalReject = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  state.value = value;
  state.state = REJECTED;
  notify(state, true);
};

var internalResolve = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  try {
    if (state.facade === value) throw TypeError("Promise can't be resolved itself");
    var then = isThenable(value);
    if (then) {
      microtask(function () {
        var wrapper = { done: false };
        try {
          call(then, value,
            bind(internalResolve, wrapper, state),
            bind(internalReject, wrapper, state)
          );
        } catch (error) {
          internalReject(wrapper, error, state);
        }
      });
    } else {
      state.value = value;
      state.state = FULFILLED;
      notify(state, false);
    }
  } catch (error) {
    internalReject({ done: false }, error, state);
  }
};

// constructor polyfill
if (FORCED) {
  // 25.4.3.1 Promise(executor)
  PromiseConstructor = function Promise(executor) {
    anInstance(this, PromisePrototype);
    aCallable(executor);
    call(Internal, this);
    var state = getInternalState(this);
    try {
      executor(bind(internalResolve, state), bind(internalReject, state));
    } catch (error) {
      internalReject(state, error);
    }
  };
  PromisePrototype = PromiseConstructor.prototype;
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  Internal = function Promise(executor) {
    setInternalState(this, {
      type: PROMISE,
      done: false,
      notified: false,
      parent: false,
      reactions: [],
      rejection: false,
      state: PENDING,
      value: undefined
    });
  };
  Internal.prototype = redefineAll(PromisePrototype, {
    // `Promise.prototype.then` method
    // https://tc39.es/ecma262/#sec-promise.prototype.then
    then: function then(onFulfilled, onRejected) {
      var state = getInternalPromiseState(this);
      var reactions = state.reactions;
      var reaction = newPromiseCapability(speciesConstructor(this, PromiseConstructor));
      reaction.ok = isCallable(onFulfilled) ? onFulfilled : true;
      reaction.fail = isCallable(onRejected) && onRejected;
      reaction.domain = IS_NODE ? process.domain : undefined;
      state.parent = true;
      reactions[reactions.length] = reaction;
      if (state.state != PENDING) notify(state, false);
      return reaction.promise;
    },
    // `Promise.prototype.catch` method
    // https://tc39.es/ecma262/#sec-promise.prototype.catch
    'catch': function (onRejected) {
      return this.then(undefined, onRejected);
    }
  });
  OwnPromiseCapability = function () {
    var promise = new Internal();
    var state = getInternalState(promise);
    this.promise = promise;
    this.resolve = bind(internalResolve, state);
    this.reject = bind(internalReject, state);
  };
  newPromiseCapabilityModule.f = newPromiseCapability = function (C) {
    return C === PromiseConstructor || C === PromiseWrapper
      ? new OwnPromiseCapability(C)
      : newGenericPromiseCapability(C);
  };

  if (!IS_PURE && isCallable(NativePromise) && NativePromisePrototype !== Object.prototype) {
    nativeThen = NativePromisePrototype.then;

    if (!SUBCLASSING) {
      // make `Promise#then` return a polyfilled `Promise` for native promise-based APIs
      redefine(NativePromisePrototype, 'then', function then(onFulfilled, onRejected) {
        var that = this;
        return new PromiseConstructor(function (resolve, reject) {
          call(nativeThen, that, resolve, reject);
        }).then(onFulfilled, onRejected);
      // https://github.com/zloirock/core-js/issues/640
      }, { unsafe: true });

      // makes sure that native promise-based APIs `Promise#catch` properly works with patched `Promise#then`
      redefine(NativePromisePrototype, 'catch', PromisePrototype['catch'], { unsafe: true });
    }

    // make `.constructor === Promise` work for native promise-based APIs
    try {
      delete NativePromisePrototype.constructor;
    } catch (error) { /* empty */ }

    // make `instanceof Promise` work for native promise-based APIs
    if (setPrototypeOf) {
      setPrototypeOf(NativePromisePrototype, PromisePrototype);
    }
  }
}

$({ global: true, wrap: true, forced: FORCED }, {
  Promise: PromiseConstructor
});

setToStringTag(PromiseConstructor, PROMISE, false, true);
setSpecies(PROMISE);

PromiseWrapper = getBuiltIn(PROMISE);

// statics
$({ target: PROMISE, stat: true, forced: FORCED }, {
  // `Promise.reject` method
  // https://tc39.es/ecma262/#sec-promise.reject
  reject: function reject(r) {
    var capability = newPromiseCapability(this);
    call(capability.reject, undefined, r);
    return capability.promise;
  }
});

$({ target: PROMISE, stat: true, forced: IS_PURE || FORCED }, {
  // `Promise.resolve` method
  // https://tc39.es/ecma262/#sec-promise.resolve
  resolve: function resolve(x) {
    return promiseResolve(IS_PURE && this === PromiseWrapper ? PromiseConstructor : this, x);
  }
});

$({ target: PROMISE, stat: true, forced: INCORRECT_ITERATION }, {
  // `Promise.all` method
  // https://tc39.es/ecma262/#sec-promise.all
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aCallable(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        remaining++;
        call($promiseResolve, C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  },
  // `Promise.race` method
  // https://tc39.es/ecma262/#sec-promise.race
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapability(C);
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aCallable(C.resolve);
      iterate(iterable, function (promise) {
        call($promiseResolve, C, promise).then(capability.resolve, reject);
      });
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});


/***/ }),

/***/ 61726:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var exec = __webpack_require__(49820);

// `RegExp.prototype.exec` method
// https://tc39.es/ecma262/#sec-regexp.prototype.exec
$({ target: 'RegExp', proto: true, forced: /./.exec !== exec }, {
  exec: exec
});


/***/ }),

/***/ 74516:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var DESCRIPTORS = __webpack_require__(15567);
var objectDefinePropertyModule = __webpack_require__(95892);
var regExpFlags = __webpack_require__(21182);
var fails = __webpack_require__(47044);

var RegExpPrototype = RegExp.prototype;

var FORCED = DESCRIPTORS && fails(function () {
  // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
  return Object.getOwnPropertyDescriptor(RegExpPrototype, 'flags').get.call({ dotAll: true, sticky: true }) !== 'sy';
});

// `RegExp.prototype.flags` getter
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags
if (FORCED) objectDefinePropertyModule.f(RegExpPrototype, 'flags', {
  configurable: true,
  get: regExpFlags
});


/***/ }),

/***/ 57114:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var uncurryThis = __webpack_require__(38347);
var PROPER_FUNCTION_NAME = (__webpack_require__(7081).PROPER);
var redefine = __webpack_require__(13711);
var anObject = __webpack_require__(34984);
var isPrototypeOf = __webpack_require__(70176);
var $toString = __webpack_require__(25096);
var fails = __webpack_require__(47044);
var regExpFlags = __webpack_require__(21182);

var TO_STRING = 'toString';
var RegExpPrototype = RegExp.prototype;
var n$ToString = RegExpPrototype[TO_STRING];
var getFlags = uncurryThis(regExpFlags);

var NOT_GENERIC = fails(function () { return n$ToString.call({ source: 'a', flags: 'b' }) != '/a/b'; });
// FF44- RegExp#toString has a wrong name
var INCORRECT_NAME = PROPER_FUNCTION_NAME && n$ToString.name != TO_STRING;

// `RegExp.prototype.toString` method
// https://tc39.es/ecma262/#sec-regexp.prototype.tostring
if (NOT_GENERIC || INCORRECT_NAME) {
  redefine(RegExp.prototype, TO_STRING, function toString() {
    var R = anObject(this);
    var p = $toString(R.source);
    var rf = R.flags;
    var f = $toString(rf === undefined && isPrototypeOf(RegExpPrototype, R) && !('flags' in RegExpPrototype) ? getFlags(R) : rf);
    return '/' + p + '/' + f;
  }, { unsafe: true });
}


/***/ }),

/***/ 76014:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var collection = __webpack_require__(36673);
var collectionStrong = __webpack_require__(9649);

// `Set` constructor
// https://tc39.es/ecma262/#sec-set-objects
collection('Set', function (init) {
  return function Set() { return init(this, arguments.length ? arguments[0] : undefined); };
}, collectionStrong);


/***/ }),

/***/ 28356:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var createHTML = __webpack_require__(91159);
var forcedStringHTMLMethod = __webpack_require__(7452);

// `String.prototype.bold` method
// https://tc39.es/ecma262/#sec-string.prototype.bold
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('bold') }, {
  bold: function bold() {
    return createHTML(this, 'b', '', '');
  }
});


/***/ }),

/***/ 26663:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var codeAt = (__webpack_require__(69510).codeAt);

// `String.prototype.codePointAt` method
// https://tc39.es/ecma262/#sec-string.prototype.codepointat
$({ target: 'String', proto: true }, {
  codePointAt: function codePointAt(pos) {
    return codeAt(this, pos);
  }
});


/***/ }),

/***/ 65578:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var toAbsoluteIndex = __webpack_require__(74841);

var RangeError = global.RangeError;
var fromCharCode = String.fromCharCode;
// eslint-disable-next-line es/no-string-fromcodepoint -- required for testing
var $fromCodePoint = String.fromCodePoint;
var join = uncurryThis([].join);

// length should be 1, old FF problem
var INCORRECT_LENGTH = !!$fromCodePoint && $fromCodePoint.length != 1;

// `String.fromCodePoint` method
// https://tc39.es/ecma262/#sec-string.fromcodepoint
$({ target: 'String', stat: true, forced: INCORRECT_LENGTH }, {
  // eslint-disable-next-line no-unused-vars -- required for `.length`
  fromCodePoint: function fromCodePoint(x) {
    var elements = [];
    var length = arguments.length;
    var i = 0;
    var code;
    while (length > i) {
      code = +arguments[i++];
      if (toAbsoluteIndex(code, 0x10FFFF) !== code) throw RangeError(code + ' is not a valid code point');
      elements[i] = code < 0x10000
        ? fromCharCode(code)
        : fromCharCode(((code -= 0x10000) >> 10) + 0xD800, code % 0x400 + 0xDC00);
    } return join(elements, '');
  }
});


/***/ }),

/***/ 47458:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var uncurryThis = __webpack_require__(38347);
var notARegExp = __webpack_require__(93666);
var requireObjectCoercible = __webpack_require__(83943);
var toString = __webpack_require__(25096);
var correctIsRegExpLogic = __webpack_require__(91151);

var stringIndexOf = uncurryThis(''.indexOf);

// `String.prototype.includes` method
// https://tc39.es/ecma262/#sec-string.prototype.includes
$({ target: 'String', proto: true, forced: !correctIsRegExpLogic('includes') }, {
  includes: function includes(searchString /* , position = 0 */) {
    return !!~stringIndexOf(
      toString(requireObjectCoercible(this)),
      toString(notARegExp(searchString)),
      arguments.length > 1 ? arguments[1] : undefined
    );
  }
});


/***/ }),

/***/ 62046:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var createHTML = __webpack_require__(91159);
var forcedStringHTMLMethod = __webpack_require__(7452);

// `String.prototype.italics` method
// https://tc39.es/ecma262/#sec-string.prototype.italics
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('italics') }, {
  italics: function italics() {
    return createHTML(this, 'i', '', '');
  }
});


/***/ }),

/***/ 58281:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var charAt = (__webpack_require__(69510).charAt);
var toString = __webpack_require__(25096);
var InternalStateModule = __webpack_require__(70172);
var defineIterator = __webpack_require__(97001);

var STRING_ITERATOR = 'String Iterator';
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(STRING_ITERATOR);

// `String.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-string.prototype-@@iterator
defineIterator(String, 'String', function (iterated) {
  setInternalState(this, {
    type: STRING_ITERATOR,
    string: toString(iterated),
    index: 0
  });
// `%StringIteratorPrototype%.next` method
// https://tc39.es/ecma262/#sec-%stringiteratorprototype%.next
}, function next() {
  var state = getInternalState(this);
  var string = state.string;
  var index = state.index;
  var point;
  if (index >= string.length) return { value: undefined, done: true };
  point = charAt(string, index);
  state.index += point.length;
  return { value: point, done: false };
});


/***/ }),

/***/ 47259:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var createHTML = __webpack_require__(91159);
var forcedStringHTMLMethod = __webpack_require__(7452);

// `String.prototype.link` method
// https://tc39.es/ecma262/#sec-string.prototype.link
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('link') }, {
  link: function link(url) {
    return createHTML(this, 'a', 'href', url);
  }
});


/***/ }),

/***/ 71768:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

/* eslint-disable es/no-string-prototype-matchall -- safe */
var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var createIteratorConstructor = __webpack_require__(13945);
var requireObjectCoercible = __webpack_require__(83943);
var toLength = __webpack_require__(23417);
var toString = __webpack_require__(25096);
var anObject = __webpack_require__(34984);
var classof = __webpack_require__(93975);
var isPrototypeOf = __webpack_require__(70176);
var isRegExp = __webpack_require__(28831);
var regExpFlags = __webpack_require__(21182);
var getMethod = __webpack_require__(51839);
var redefine = __webpack_require__(13711);
var fails = __webpack_require__(47044);
var wellKnownSymbol = __webpack_require__(38688);
var speciesConstructor = __webpack_require__(27754);
var advanceStringIndex = __webpack_require__(36352);
var regExpExec = __webpack_require__(66723);
var InternalStateModule = __webpack_require__(70172);
var IS_PURE = __webpack_require__(63432);

var MATCH_ALL = wellKnownSymbol('matchAll');
var REGEXP_STRING = 'RegExp String';
var REGEXP_STRING_ITERATOR = REGEXP_STRING + ' Iterator';
var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(REGEXP_STRING_ITERATOR);
var RegExpPrototype = RegExp.prototype;
var TypeError = global.TypeError;
var getFlags = uncurryThis(regExpFlags);
var stringIndexOf = uncurryThis(''.indexOf);
var un$MatchAll = uncurryThis(''.matchAll);

var WORKS_WITH_NON_GLOBAL_REGEX = !!un$MatchAll && !fails(function () {
  un$MatchAll('a', /./);
});

var $RegExpStringIterator = createIteratorConstructor(function RegExpStringIterator(regexp, string, $global, fullUnicode) {
  setInternalState(this, {
    type: REGEXP_STRING_ITERATOR,
    regexp: regexp,
    string: string,
    global: $global,
    unicode: fullUnicode,
    done: false
  });
}, REGEXP_STRING, function next() {
  var state = getInternalState(this);
  if (state.done) return { value: undefined, done: true };
  var R = state.regexp;
  var S = state.string;
  var match = regExpExec(R, S);
  if (match === null) return { value: undefined, done: state.done = true };
  if (state.global) {
    if (toString(match[0]) === '') R.lastIndex = advanceStringIndex(S, toLength(R.lastIndex), state.unicode);
    return { value: match, done: false };
  }
  state.done = true;
  return { value: match, done: false };
});

var $matchAll = function (string) {
  var R = anObject(this);
  var S = toString(string);
  var C, flagsValue, flags, matcher, $global, fullUnicode;
  C = speciesConstructor(R, RegExp);
  flagsValue = R.flags;
  if (flagsValue === undefined && isPrototypeOf(RegExpPrototype, R) && !('flags' in RegExpPrototype)) {
    flagsValue = getFlags(R);
  }
  flags = flagsValue === undefined ? '' : toString(flagsValue);
  matcher = new C(C === RegExp ? R.source : R, flags);
  $global = !!~stringIndexOf(flags, 'g');
  fullUnicode = !!~stringIndexOf(flags, 'u');
  matcher.lastIndex = toLength(R.lastIndex);
  return new $RegExpStringIterator(matcher, S, $global, fullUnicode);
};

// `String.prototype.matchAll` method
// https://tc39.es/ecma262/#sec-string.prototype.matchall
$({ target: 'String', proto: true, forced: WORKS_WITH_NON_GLOBAL_REGEX }, {
  matchAll: function matchAll(regexp) {
    var O = requireObjectCoercible(this);
    var flags, S, matcher, rx;
    if (regexp != null) {
      if (isRegExp(regexp)) {
        flags = toString(requireObjectCoercible('flags' in RegExpPrototype
          ? regexp.flags
          : getFlags(regexp)
        ));
        if (!~stringIndexOf(flags, 'g')) throw TypeError('`.matchAll` does not allow non-global regexes');
      }
      if (WORKS_WITH_NON_GLOBAL_REGEX) return un$MatchAll(O, regexp);
      matcher = getMethod(regexp, MATCH_ALL);
      if (matcher === undefined && IS_PURE && classof(regexp) == 'RegExp') matcher = $matchAll;
      if (matcher) return call(matcher, regexp, O);
    } else if (WORKS_WITH_NON_GLOBAL_REGEX) return un$MatchAll(O, regexp);
    S = toString(O);
    rx = new RegExp(regexp, 'g');
    return IS_PURE ? call($matchAll, rx, S) : rx[MATCH_ALL](S);
  }
});

IS_PURE || MATCH_ALL in RegExpPrototype || redefine(RegExpPrototype, MATCH_ALL, $matchAll);


/***/ }),

/***/ 6422:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var call = __webpack_require__(2834);
var fixRegExpWellKnownSymbolLogic = __webpack_require__(11813);
var anObject = __webpack_require__(34984);
var toLength = __webpack_require__(23417);
var toString = __webpack_require__(25096);
var requireObjectCoercible = __webpack_require__(83943);
var getMethod = __webpack_require__(51839);
var advanceStringIndex = __webpack_require__(36352);
var regExpExec = __webpack_require__(66723);

// @@match logic
fixRegExpWellKnownSymbolLogic('match', function (MATCH, nativeMatch, maybeCallNative) {
  return [
    // `String.prototype.match` method
    // https://tc39.es/ecma262/#sec-string.prototype.match
    function match(regexp) {
      var O = requireObjectCoercible(this);
      var matcher = regexp == undefined ? undefined : getMethod(regexp, MATCH);
      return matcher ? call(matcher, regexp, O) : new RegExp(regexp)[MATCH](toString(O));
    },
    // `RegExp.prototype[@@match]` method
    // https://tc39.es/ecma262/#sec-regexp.prototype-@@match
    function (string) {
      var rx = anObject(this);
      var S = toString(string);
      var res = maybeCallNative(nativeMatch, rx, S);

      if (res.done) return res.value;

      if (!rx.global) return regExpExec(rx, S);

      var fullUnicode = rx.unicode;
      rx.lastIndex = 0;
      var A = [];
      var n = 0;
      var result;
      while ((result = regExpExec(rx, S)) !== null) {
        var matchStr = toString(result[0]);
        A[n] = matchStr;
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
        n++;
      }
      return n === 0 ? null : A;
    }
  ];
});


/***/ }),

/***/ 28264:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var $ = __webpack_require__(56475);
var repeat = __webpack_require__(34858);

// `String.prototype.repeat` method
// https://tc39.es/ecma262/#sec-string.prototype.repeat
$({ target: 'String', proto: true }, {
  repeat: repeat
});


/***/ }),

/***/ 1593:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var requireObjectCoercible = __webpack_require__(83943);
var isCallable = __webpack_require__(94578);
var isRegExp = __webpack_require__(28831);
var toString = __webpack_require__(25096);
var getMethod = __webpack_require__(51839);
var regExpFlags = __webpack_require__(21182);
var getSubstitution = __webpack_require__(29519);
var wellKnownSymbol = __webpack_require__(38688);
var IS_PURE = __webpack_require__(63432);

var REPLACE = wellKnownSymbol('replace');
var RegExpPrototype = RegExp.prototype;
var TypeError = global.TypeError;
var getFlags = uncurryThis(regExpFlags);
var indexOf = uncurryThis(''.indexOf);
var replace = uncurryThis(''.replace);
var stringSlice = uncurryThis(''.slice);
var max = Math.max;

var stringIndexOf = function (string, searchValue, fromIndex) {
  if (fromIndex > string.length) return -1;
  if (searchValue === '') return fromIndex;
  return indexOf(string, searchValue, fromIndex);
};

// `String.prototype.replaceAll` method
// https://tc39.es/ecma262/#sec-string.prototype.replaceall
$({ target: 'String', proto: true }, {
  replaceAll: function replaceAll(searchValue, replaceValue) {
    var O = requireObjectCoercible(this);
    var IS_REG_EXP, flags, replacer, string, searchString, functionalReplace, searchLength, advanceBy, replacement;
    var position = 0;
    var endOfLastMatch = 0;
    var result = '';
    if (searchValue != null) {
      IS_REG_EXP = isRegExp(searchValue);
      if (IS_REG_EXP) {
        flags = toString(requireObjectCoercible('flags' in RegExpPrototype
          ? searchValue.flags
          : getFlags(searchValue)
        ));
        if (!~indexOf(flags, 'g')) throw TypeError('`.replaceAll` does not allow non-global regexes');
      }
      replacer = getMethod(searchValue, REPLACE);
      if (replacer) {
        return call(replacer, searchValue, O, replaceValue);
      } else if (IS_PURE && IS_REG_EXP) {
        return replace(toString(O), searchValue, replaceValue);
      }
    }
    string = toString(O);
    searchString = toString(searchValue);
    functionalReplace = isCallable(replaceValue);
    if (!functionalReplace) replaceValue = toString(replaceValue);
    searchLength = searchString.length;
    advanceBy = max(1, searchLength);
    position = stringIndexOf(string, searchString, 0);
    while (position !== -1) {
      replacement = functionalReplace
        ? toString(replaceValue(searchString, position, string))
        : getSubstitution(searchString, string, position, [], undefined, replaceValue);
      result += stringSlice(string, endOfLastMatch, position) + replacement;
      endOfLastMatch = position + searchLength;
      position = stringIndexOf(string, searchString, position + advanceBy);
    }
    if (endOfLastMatch < string.length) {
      result += stringSlice(string, endOfLastMatch);
    }
    return result;
  }
});


/***/ }),

/***/ 46467:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var apply = __webpack_require__(58448);
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var fixRegExpWellKnownSymbolLogic = __webpack_require__(11813);
var fails = __webpack_require__(47044);
var anObject = __webpack_require__(34984);
var isCallable = __webpack_require__(94578);
var toIntegerOrInfinity = __webpack_require__(26882);
var toLength = __webpack_require__(23417);
var toString = __webpack_require__(25096);
var requireObjectCoercible = __webpack_require__(83943);
var advanceStringIndex = __webpack_require__(36352);
var getMethod = __webpack_require__(51839);
var getSubstitution = __webpack_require__(29519);
var regExpExec = __webpack_require__(66723);
var wellKnownSymbol = __webpack_require__(38688);

var REPLACE = wellKnownSymbol('replace');
var max = Math.max;
var min = Math.min;
var concat = uncurryThis([].concat);
var push = uncurryThis([].push);
var stringIndexOf = uncurryThis(''.indexOf);
var stringSlice = uncurryThis(''.slice);

var maybeToString = function (it) {
  return it === undefined ? it : String(it);
};

// IE <= 11 replaces $0 with the whole match, as if it was $&
// https://stackoverflow.com/questions/6024666/getting-ie-to-replace-a-regex-with-the-literal-string-0
var REPLACE_KEEPS_$0 = (function () {
  // eslint-disable-next-line regexp/prefer-escape-replacement-dollar-char -- required for testing
  return 'a'.replace(/./, '$0') === '$0';
})();

// Safari <= 13.0.3(?) substitutes nth capture where n>m with an empty string
var REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE = (function () {
  if (/./[REPLACE]) {
    return /./[REPLACE]('a', '$0') === '';
  }
  return false;
})();

var REPLACE_SUPPORTS_NAMED_GROUPS = !fails(function () {
  var re = /./;
  re.exec = function () {
    var result = [];
    result.groups = { a: '7' };
    return result;
  };
  // eslint-disable-next-line regexp/no-useless-dollar-replacements -- false positive
  return ''.replace(re, '$<a>') !== '7';
});

// @@replace logic
fixRegExpWellKnownSymbolLogic('replace', function (_, nativeReplace, maybeCallNative) {
  var UNSAFE_SUBSTITUTE = REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE ? '$' : '$0';

  return [
    // `String.prototype.replace` method
    // https://tc39.es/ecma262/#sec-string.prototype.replace
    function replace(searchValue, replaceValue) {
      var O = requireObjectCoercible(this);
      var replacer = searchValue == undefined ? undefined : getMethod(searchValue, REPLACE);
      return replacer
        ? call(replacer, searchValue, O, replaceValue)
        : call(nativeReplace, toString(O), searchValue, replaceValue);
    },
    // `RegExp.prototype[@@replace]` method
    // https://tc39.es/ecma262/#sec-regexp.prototype-@@replace
    function (string, replaceValue) {
      var rx = anObject(this);
      var S = toString(string);

      if (
        typeof replaceValue == 'string' &&
        stringIndexOf(replaceValue, UNSAFE_SUBSTITUTE) === -1 &&
        stringIndexOf(replaceValue, '$<') === -1
      ) {
        var res = maybeCallNative(nativeReplace, rx, S, replaceValue);
        if (res.done) return res.value;
      }

      var functionalReplace = isCallable(replaceValue);
      if (!functionalReplace) replaceValue = toString(replaceValue);

      var global = rx.global;
      if (global) {
        var fullUnicode = rx.unicode;
        rx.lastIndex = 0;
      }
      var results = [];
      while (true) {
        var result = regExpExec(rx, S);
        if (result === null) break;

        push(results, result);
        if (!global) break;

        var matchStr = toString(result[0]);
        if (matchStr === '') rx.lastIndex = advanceStringIndex(S, toLength(rx.lastIndex), fullUnicode);
      }

      var accumulatedResult = '';
      var nextSourcePosition = 0;
      for (var i = 0; i < results.length; i++) {
        result = results[i];

        var matched = toString(result[0]);
        var position = max(min(toIntegerOrInfinity(result.index), S.length), 0);
        var captures = [];
        // NOTE: This is equivalent to
        //   captures = result.slice(1).map(maybeToString)
        // but for some reason `nativeSlice.call(result, 1, result.length)` (called in
        // the slice polyfill when slicing native arrays) "doesn't work" in safari 9 and
        // causes a crash (https://pastebin.com/N21QzeQA) when trying to debug it.
        for (var j = 1; j < result.length; j++) push(captures, maybeToString(result[j]));
        var namedCaptures = result.groups;
        if (functionalReplace) {
          var replacerArgs = concat([matched], captures, position, S);
          if (namedCaptures !== undefined) push(replacerArgs, namedCaptures);
          var replacement = toString(apply(replaceValue, undefined, replacerArgs));
        } else {
          replacement = getSubstitution(matched, S, position, captures, namedCaptures, replaceValue);
        }
        if (position >= nextSourcePosition) {
          accumulatedResult += stringSlice(S, nextSourcePosition, position) + replacement;
          nextSourcePosition = position + matched.length;
        }
      }
      return accumulatedResult + stringSlice(S, nextSourcePosition);
    }
  ];
}, !REPLACE_SUPPORTS_NAMED_GROUPS || !REPLACE_KEEPS_$0 || REGEXP_REPLACE_SUBSTITUTES_UNDEFINED_CAPTURE);


/***/ }),

/***/ 7851:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var apply = __webpack_require__(58448);
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var fixRegExpWellKnownSymbolLogic = __webpack_require__(11813);
var isRegExp = __webpack_require__(28831);
var anObject = __webpack_require__(34984);
var requireObjectCoercible = __webpack_require__(83943);
var speciesConstructor = __webpack_require__(27754);
var advanceStringIndex = __webpack_require__(36352);
var toLength = __webpack_require__(23417);
var toString = __webpack_require__(25096);
var getMethod = __webpack_require__(51839);
var arraySlice = __webpack_require__(73163);
var callRegExpExec = __webpack_require__(66723);
var regexpExec = __webpack_require__(49820);
var stickyHelpers = __webpack_require__(74846);
var fails = __webpack_require__(47044);

var UNSUPPORTED_Y = stickyHelpers.UNSUPPORTED_Y;
var MAX_UINT32 = 0xFFFFFFFF;
var min = Math.min;
var $push = [].push;
var exec = uncurryThis(/./.exec);
var push = uncurryThis($push);
var stringSlice = uncurryThis(''.slice);

// Chrome 51 has a buggy "split" implementation when RegExp#exec !== nativeExec
// Weex JS has frozen built-in prototypes, so use try / catch wrapper
var SPLIT_WORKS_WITH_OVERWRITTEN_EXEC = !fails(function () {
  // eslint-disable-next-line regexp/no-empty-group -- required for testing
  var re = /(?:)/;
  var originalExec = re.exec;
  re.exec = function () { return originalExec.apply(this, arguments); };
  var result = 'ab'.split(re);
  return result.length !== 2 || result[0] !== 'a' || result[1] !== 'b';
});

// @@split logic
fixRegExpWellKnownSymbolLogic('split', function (SPLIT, nativeSplit, maybeCallNative) {
  var internalSplit;
  if (
    'abbc'.split(/(b)*/)[1] == 'c' ||
    // eslint-disable-next-line regexp/no-empty-group -- required for testing
    'test'.split(/(?:)/, -1).length != 4 ||
    'ab'.split(/(?:ab)*/).length != 2 ||
    '.'.split(/(.?)(.?)/).length != 4 ||
    // eslint-disable-next-line regexp/no-empty-capturing-group, regexp/no-empty-group -- required for testing
    '.'.split(/()()/).length > 1 ||
    ''.split(/.?/).length
  ) {
    // based on es5-shim implementation, need to rework it
    internalSplit = function (separator, limit) {
      var string = toString(requireObjectCoercible(this));
      var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
      if (lim === 0) return [];
      if (separator === undefined) return [string];
      // If `separator` is not a regex, use native split
      if (!isRegExp(separator)) {
        return call(nativeSplit, string, separator, lim);
      }
      var output = [];
      var flags = (separator.ignoreCase ? 'i' : '') +
                  (separator.multiline ? 'm' : '') +
                  (separator.unicode ? 'u' : '') +
                  (separator.sticky ? 'y' : '');
      var lastLastIndex = 0;
      // Make `global` and avoid `lastIndex` issues by working with a copy
      var separatorCopy = new RegExp(separator.source, flags + 'g');
      var match, lastIndex, lastLength;
      while (match = call(regexpExec, separatorCopy, string)) {
        lastIndex = separatorCopy.lastIndex;
        if (lastIndex > lastLastIndex) {
          push(output, stringSlice(string, lastLastIndex, match.index));
          if (match.length > 1 && match.index < string.length) apply($push, output, arraySlice(match, 1));
          lastLength = match[0].length;
          lastLastIndex = lastIndex;
          if (output.length >= lim) break;
        }
        if (separatorCopy.lastIndex === match.index) separatorCopy.lastIndex++; // Avoid an infinite loop
      }
      if (lastLastIndex === string.length) {
        if (lastLength || !exec(separatorCopy, '')) push(output, '');
      } else push(output, stringSlice(string, lastLastIndex));
      return output.length > lim ? arraySlice(output, 0, lim) : output;
    };
  // Chakra, V8
  } else if ('0'.split(undefined, 0).length) {
    internalSplit = function (separator, limit) {
      return separator === undefined && limit === 0 ? [] : call(nativeSplit, this, separator, limit);
    };
  } else internalSplit = nativeSplit;

  return [
    // `String.prototype.split` method
    // https://tc39.es/ecma262/#sec-string.prototype.split
    function split(separator, limit) {
      var O = requireObjectCoercible(this);
      var splitter = separator == undefined ? undefined : getMethod(separator, SPLIT);
      return splitter
        ? call(splitter, separator, O, limit)
        : call(internalSplit, toString(O), separator, limit);
    },
    // `RegExp.prototype[@@split]` method
    // https://tc39.es/ecma262/#sec-regexp.prototype-@@split
    //
    // NOTE: This cannot be properly polyfilled in engines that don't support
    // the 'y' flag.
    function (string, limit) {
      var rx = anObject(this);
      var S = toString(string);
      var res = maybeCallNative(internalSplit, rx, S, limit, internalSplit !== nativeSplit);

      if (res.done) return res.value;

      var C = speciesConstructor(rx, RegExp);

      var unicodeMatching = rx.unicode;
      var flags = (rx.ignoreCase ? 'i' : '') +
                  (rx.multiline ? 'm' : '') +
                  (rx.unicode ? 'u' : '') +
                  (UNSUPPORTED_Y ? 'g' : 'y');

      // ^(? + rx + ) is needed, in combination with some S slicing, to
      // simulate the 'y' flag.
      var splitter = new C(UNSUPPORTED_Y ? '^(?:' + rx.source + ')' : rx, flags);
      var lim = limit === undefined ? MAX_UINT32 : limit >>> 0;
      if (lim === 0) return [];
      if (S.length === 0) return callRegExpExec(splitter, S) === null ? [S] : [];
      var p = 0;
      var q = 0;
      var A = [];
      while (q < S.length) {
        splitter.lastIndex = UNSUPPORTED_Y ? 0 : q;
        var z = callRegExpExec(splitter, UNSUPPORTED_Y ? stringSlice(S, q) : S);
        var e;
        if (
          z === null ||
          (e = min(toLength(splitter.lastIndex + (UNSUPPORTED_Y ? q : 0)), S.length)) === p
        ) {
          q = advanceStringIndex(S, q, unicodeMatching);
        } else {
          push(A, stringSlice(S, p, q));
          if (A.length === lim) return A;
          for (var i = 1; i <= z.length - 1; i++) {
            push(A, z[i]);
            if (A.length === lim) return A;
          }
          q = p = e;
        }
      }
      push(A, stringSlice(S, p));
      return A;
    }
  ];
}, !SPLIT_WORKS_WITH_OVERWRITTEN_EXEC, UNSUPPORTED_Y);


/***/ }),

/***/ 18425:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var createHTML = __webpack_require__(91159);
var forcedStringHTMLMethod = __webpack_require__(7452);

// `String.prototype.strike` method
// https://tc39.es/ecma262/#sec-string.prototype.strike
$({ target: 'String', proto: true, forced: forcedStringHTMLMethod('strike') }, {
  strike: function strike() {
    return createHTML(this, 'strike', '', '');
  }
});


/***/ }),

/***/ 72095:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var $trim = (__webpack_require__(29841).trim);
var forcedStringTrimMethod = __webpack_require__(68899);

// `String.prototype.trim` method
// https://tc39.es/ecma262/#sec-string.prototype.trim
$({ target: 'String', proto: true, forced: forcedStringTrimMethod('trim') }, {
  trim: function trim() {
    return $trim(this);
  }
});


/***/ }),

/***/ 35877:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var defineWellKnownSymbol = __webpack_require__(46042);

// `Symbol.asyncIterator` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.asynciterator
defineWellKnownSymbol('asyncIterator');


/***/ }),

/***/ 73844:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// `Symbol.prototype.description` getter
// https://tc39.es/ecma262/#sec-symbol.prototype.description

var $ = __webpack_require__(56475);
var DESCRIPTORS = __webpack_require__(15567);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var hasOwn = __webpack_require__(20340);
var isCallable = __webpack_require__(94578);
var isPrototypeOf = __webpack_require__(70176);
var toString = __webpack_require__(25096);
var defineProperty = (__webpack_require__(95892).f);
var copyConstructorProperties = __webpack_require__(2675);

var NativeSymbol = global.Symbol;
var SymbolPrototype = NativeSymbol && NativeSymbol.prototype;

if (DESCRIPTORS && isCallable(NativeSymbol) && (!('description' in SymbolPrototype) ||
  // Safari 12 bug
  NativeSymbol().description !== undefined
)) {
  var EmptyStringDescriptionStore = {};
  // wrap Symbol constructor for correct work with undefined description
  var SymbolWrapper = function Symbol() {
    var description = arguments.length < 1 || arguments[0] === undefined ? undefined : toString(arguments[0]);
    var result = isPrototypeOf(SymbolPrototype, this)
      ? new NativeSymbol(description)
      // in Edge 13, String(Symbol(undefined)) === 'Symbol(undefined)'
      : description === undefined ? NativeSymbol() : NativeSymbol(description);
    if (description === '') EmptyStringDescriptionStore[result] = true;
    return result;
  };

  copyConstructorProperties(SymbolWrapper, NativeSymbol);
  SymbolWrapper.prototype = SymbolPrototype;
  SymbolPrototype.constructor = SymbolWrapper;

  var NATIVE_SYMBOL = String(NativeSymbol('test')) == 'Symbol(test)';
  var symbolToString = uncurryThis(SymbolPrototype.toString);
  var symbolValueOf = uncurryThis(SymbolPrototype.valueOf);
  var regexp = /^Symbol\((.*)\)[^)]+$/;
  var replace = uncurryThis(''.replace);
  var stringSlice = uncurryThis(''.slice);

  defineProperty(SymbolPrototype, 'description', {
    configurable: true,
    get: function description() {
      var symbol = symbolValueOf(this);
      var string = symbolToString(symbol);
      if (hasOwn(EmptyStringDescriptionStore, symbol)) return '';
      var desc = NATIVE_SYMBOL ? stringSlice(string, 7, -1) : replace(string, regexp, '$1');
      return desc === '' ? undefined : desc;
    }
  });

  $({ global: true, forced: true }, {
    Symbol: SymbolWrapper
  });
}


/***/ }),

/***/ 69330:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var defineWellKnownSymbol = __webpack_require__(46042);

// `Symbol.iterator` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.iterator
defineWellKnownSymbol('iterator');


/***/ }),

/***/ 65292:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var global = __webpack_require__(32010);
var getBuiltIn = __webpack_require__(38486);
var apply = __webpack_require__(58448);
var call = __webpack_require__(2834);
var uncurryThis = __webpack_require__(38347);
var IS_PURE = __webpack_require__(63432);
var DESCRIPTORS = __webpack_require__(15567);
var NATIVE_SYMBOL = __webpack_require__(46887);
var fails = __webpack_require__(47044);
var hasOwn = __webpack_require__(20340);
var isArray = __webpack_require__(59113);
var isCallable = __webpack_require__(94578);
var isObject = __webpack_require__(24517);
var isPrototypeOf = __webpack_require__(70176);
var isSymbol = __webpack_require__(46290);
var anObject = __webpack_require__(34984);
var toObject = __webpack_require__(43162);
var toIndexedObject = __webpack_require__(98086);
var toPropertyKey = __webpack_require__(63918);
var $toString = __webpack_require__(25096);
var createPropertyDescriptor = __webpack_require__(97841);
var nativeObjectCreate = __webpack_require__(10819);
var objectKeys = __webpack_require__(84675);
var getOwnPropertyNamesModule = __webpack_require__(6611);
var getOwnPropertyNamesExternal = __webpack_require__(8807);
var getOwnPropertySymbolsModule = __webpack_require__(61146);
var getOwnPropertyDescriptorModule = __webpack_require__(72062);
var definePropertyModule = __webpack_require__(95892);
var propertyIsEnumerableModule = __webpack_require__(55574);
var arraySlice = __webpack_require__(73163);
var redefine = __webpack_require__(13711);
var shared = __webpack_require__(464);
var sharedKey = __webpack_require__(82194);
var hiddenKeys = __webpack_require__(90682);
var uid = __webpack_require__(46859);
var wellKnownSymbol = __webpack_require__(38688);
var wrappedWellKnownSymbolModule = __webpack_require__(75960);
var defineWellKnownSymbol = __webpack_require__(46042);
var setToStringTag = __webpack_require__(15216);
var InternalStateModule = __webpack_require__(70172);
var $forEach = (__webpack_require__(91102).forEach);

var HIDDEN = sharedKey('hidden');
var SYMBOL = 'Symbol';
var PROTOTYPE = 'prototype';
var TO_PRIMITIVE = wellKnownSymbol('toPrimitive');

var setInternalState = InternalStateModule.set;
var getInternalState = InternalStateModule.getterFor(SYMBOL);

var ObjectPrototype = Object[PROTOTYPE];
var $Symbol = global.Symbol;
var SymbolPrototype = $Symbol && $Symbol[PROTOTYPE];
var TypeError = global.TypeError;
var QObject = global.QObject;
var $stringify = getBuiltIn('JSON', 'stringify');
var nativeGetOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
var nativeDefineProperty = definePropertyModule.f;
var nativeGetOwnPropertyNames = getOwnPropertyNamesExternal.f;
var nativePropertyIsEnumerable = propertyIsEnumerableModule.f;
var push = uncurryThis([].push);

var AllSymbols = shared('symbols');
var ObjectPrototypeSymbols = shared('op-symbols');
var StringToSymbolRegistry = shared('string-to-symbol-registry');
var SymbolToStringRegistry = shared('symbol-to-string-registry');
var WellKnownSymbolsStore = shared('wks');

// Don't use setters in Qt Script, https://github.com/zloirock/core-js/issues/173
var USE_SETTER = !QObject || !QObject[PROTOTYPE] || !QObject[PROTOTYPE].findChild;

// fallback for old Android, https://code.google.com/p/v8/issues/detail?id=687
var setSymbolDescriptor = DESCRIPTORS && fails(function () {
  return nativeObjectCreate(nativeDefineProperty({}, 'a', {
    get: function () { return nativeDefineProperty(this, 'a', { value: 7 }).a; }
  })).a != 7;
}) ? function (O, P, Attributes) {
  var ObjectPrototypeDescriptor = nativeGetOwnPropertyDescriptor(ObjectPrototype, P);
  if (ObjectPrototypeDescriptor) delete ObjectPrototype[P];
  nativeDefineProperty(O, P, Attributes);
  if (ObjectPrototypeDescriptor && O !== ObjectPrototype) {
    nativeDefineProperty(ObjectPrototype, P, ObjectPrototypeDescriptor);
  }
} : nativeDefineProperty;

var wrap = function (tag, description) {
  var symbol = AllSymbols[tag] = nativeObjectCreate(SymbolPrototype);
  setInternalState(symbol, {
    type: SYMBOL,
    tag: tag,
    description: description
  });
  if (!DESCRIPTORS) symbol.description = description;
  return symbol;
};

var $defineProperty = function defineProperty(O, P, Attributes) {
  if (O === ObjectPrototype) $defineProperty(ObjectPrototypeSymbols, P, Attributes);
  anObject(O);
  var key = toPropertyKey(P);
  anObject(Attributes);
  if (hasOwn(AllSymbols, key)) {
    if (!Attributes.enumerable) {
      if (!hasOwn(O, HIDDEN)) nativeDefineProperty(O, HIDDEN, createPropertyDescriptor(1, {}));
      O[HIDDEN][key] = true;
    } else {
      if (hasOwn(O, HIDDEN) && O[HIDDEN][key]) O[HIDDEN][key] = false;
      Attributes = nativeObjectCreate(Attributes, { enumerable: createPropertyDescriptor(0, false) });
    } return setSymbolDescriptor(O, key, Attributes);
  } return nativeDefineProperty(O, key, Attributes);
};

var $defineProperties = function defineProperties(O, Properties) {
  anObject(O);
  var properties = toIndexedObject(Properties);
  var keys = objectKeys(properties).concat($getOwnPropertySymbols(properties));
  $forEach(keys, function (key) {
    if (!DESCRIPTORS || call($propertyIsEnumerable, properties, key)) $defineProperty(O, key, properties[key]);
  });
  return O;
};

var $create = function create(O, Properties) {
  return Properties === undefined ? nativeObjectCreate(O) : $defineProperties(nativeObjectCreate(O), Properties);
};

var $propertyIsEnumerable = function propertyIsEnumerable(V) {
  var P = toPropertyKey(V);
  var enumerable = call(nativePropertyIsEnumerable, this, P);
  if (this === ObjectPrototype && hasOwn(AllSymbols, P) && !hasOwn(ObjectPrototypeSymbols, P)) return false;
  return enumerable || !hasOwn(this, P) || !hasOwn(AllSymbols, P) || hasOwn(this, HIDDEN) && this[HIDDEN][P]
    ? enumerable : true;
};

var $getOwnPropertyDescriptor = function getOwnPropertyDescriptor(O, P) {
  var it = toIndexedObject(O);
  var key = toPropertyKey(P);
  if (it === ObjectPrototype && hasOwn(AllSymbols, key) && !hasOwn(ObjectPrototypeSymbols, key)) return;
  var descriptor = nativeGetOwnPropertyDescriptor(it, key);
  if (descriptor && hasOwn(AllSymbols, key) && !(hasOwn(it, HIDDEN) && it[HIDDEN][key])) {
    descriptor.enumerable = true;
  }
  return descriptor;
};

var $getOwnPropertyNames = function getOwnPropertyNames(O) {
  var names = nativeGetOwnPropertyNames(toIndexedObject(O));
  var result = [];
  $forEach(names, function (key) {
    if (!hasOwn(AllSymbols, key) && !hasOwn(hiddenKeys, key)) push(result, key);
  });
  return result;
};

var $getOwnPropertySymbols = function getOwnPropertySymbols(O) {
  var IS_OBJECT_PROTOTYPE = O === ObjectPrototype;
  var names = nativeGetOwnPropertyNames(IS_OBJECT_PROTOTYPE ? ObjectPrototypeSymbols : toIndexedObject(O));
  var result = [];
  $forEach(names, function (key) {
    if (hasOwn(AllSymbols, key) && (!IS_OBJECT_PROTOTYPE || hasOwn(ObjectPrototype, key))) {
      push(result, AllSymbols[key]);
    }
  });
  return result;
};

// `Symbol` constructor
// https://tc39.es/ecma262/#sec-symbol-constructor
if (!NATIVE_SYMBOL) {
  $Symbol = function Symbol() {
    if (isPrototypeOf(SymbolPrototype, this)) throw TypeError('Symbol is not a constructor');
    var description = !arguments.length || arguments[0] === undefined ? undefined : $toString(arguments[0]);
    var tag = uid(description);
    var setter = function (value) {
      if (this === ObjectPrototype) call(setter, ObjectPrototypeSymbols, value);
      if (hasOwn(this, HIDDEN) && hasOwn(this[HIDDEN], tag)) this[HIDDEN][tag] = false;
      setSymbolDescriptor(this, tag, createPropertyDescriptor(1, value));
    };
    if (DESCRIPTORS && USE_SETTER) setSymbolDescriptor(ObjectPrototype, tag, { configurable: true, set: setter });
    return wrap(tag, description);
  };

  SymbolPrototype = $Symbol[PROTOTYPE];

  redefine(SymbolPrototype, 'toString', function toString() {
    return getInternalState(this).tag;
  });

  redefine($Symbol, 'withoutSetter', function (description) {
    return wrap(uid(description), description);
  });

  propertyIsEnumerableModule.f = $propertyIsEnumerable;
  definePropertyModule.f = $defineProperty;
  getOwnPropertyDescriptorModule.f = $getOwnPropertyDescriptor;
  getOwnPropertyNamesModule.f = getOwnPropertyNamesExternal.f = $getOwnPropertyNames;
  getOwnPropertySymbolsModule.f = $getOwnPropertySymbols;

  wrappedWellKnownSymbolModule.f = function (name) {
    return wrap(wellKnownSymbol(name), name);
  };

  if (DESCRIPTORS) {
    // https://github.com/tc39/proposal-Symbol-description
    nativeDefineProperty(SymbolPrototype, 'description', {
      configurable: true,
      get: function description() {
        return getInternalState(this).description;
      }
    });
    if (!IS_PURE) {
      redefine(ObjectPrototype, 'propertyIsEnumerable', $propertyIsEnumerable, { unsafe: true });
    }
  }
}

$({ global: true, wrap: true, forced: !NATIVE_SYMBOL, sham: !NATIVE_SYMBOL }, {
  Symbol: $Symbol
});

$forEach(objectKeys(WellKnownSymbolsStore), function (name) {
  defineWellKnownSymbol(name);
});

$({ target: SYMBOL, stat: true, forced: !NATIVE_SYMBOL }, {
  // `Symbol.for` method
  // https://tc39.es/ecma262/#sec-symbol.for
  'for': function (key) {
    var string = $toString(key);
    if (hasOwn(StringToSymbolRegistry, string)) return StringToSymbolRegistry[string];
    var symbol = $Symbol(string);
    StringToSymbolRegistry[string] = symbol;
    SymbolToStringRegistry[symbol] = string;
    return symbol;
  },
  // `Symbol.keyFor` method
  // https://tc39.es/ecma262/#sec-symbol.keyfor
  keyFor: function keyFor(sym) {
    if (!isSymbol(sym)) throw TypeError(sym + ' is not a symbol');
    if (hasOwn(SymbolToStringRegistry, sym)) return SymbolToStringRegistry[sym];
  },
  useSetter: function () { USE_SETTER = true; },
  useSimple: function () { USE_SETTER = false; }
});

$({ target: 'Object', stat: true, forced: !NATIVE_SYMBOL, sham: !DESCRIPTORS }, {
  // `Object.create` method
  // https://tc39.es/ecma262/#sec-object.create
  create: $create,
  // `Object.defineProperty` method
  // https://tc39.es/ecma262/#sec-object.defineproperty
  defineProperty: $defineProperty,
  // `Object.defineProperties` method
  // https://tc39.es/ecma262/#sec-object.defineproperties
  defineProperties: $defineProperties,
  // `Object.getOwnPropertyDescriptor` method
  // https://tc39.es/ecma262/#sec-object.getownpropertydescriptors
  getOwnPropertyDescriptor: $getOwnPropertyDescriptor
});

$({ target: 'Object', stat: true, forced: !NATIVE_SYMBOL }, {
  // `Object.getOwnPropertyNames` method
  // https://tc39.es/ecma262/#sec-object.getownpropertynames
  getOwnPropertyNames: $getOwnPropertyNames,
  // `Object.getOwnPropertySymbols` method
  // https://tc39.es/ecma262/#sec-object.getownpropertysymbols
  getOwnPropertySymbols: $getOwnPropertySymbols
});

// Chrome 38 and 39 `Object.getOwnPropertySymbols` fails on primitives
// https://bugs.chromium.org/p/v8/issues/detail?id=3443
$({ target: 'Object', stat: true, forced: fails(function () { getOwnPropertySymbolsModule.f(1); }) }, {
  getOwnPropertySymbols: function getOwnPropertySymbols(it) {
    return getOwnPropertySymbolsModule.f(toObject(it));
  }
});

// `JSON.stringify` method behavior with symbols
// https://tc39.es/ecma262/#sec-json.stringify
if ($stringify) {
  var FORCED_JSON_STRINGIFY = !NATIVE_SYMBOL || fails(function () {
    var symbol = $Symbol();
    // MS Edge converts symbol values to JSON as {}
    return $stringify([symbol]) != '[null]'
      // WebKit converts symbol values to JSON as null
      || $stringify({ a: symbol }) != '{}'
      // V8 throws on boxed symbols
      || $stringify(Object(symbol)) != '{}';
  });

  $({ target: 'JSON', stat: true, forced: FORCED_JSON_STRINGIFY }, {
    // eslint-disable-next-line no-unused-vars -- required for `.length`
    stringify: function stringify(it, replacer, space) {
      var args = arraySlice(arguments);
      var $replacer = replacer;
      if (!isObject(replacer) && it === undefined || isSymbol(it)) return; // IE8 returns string on undefined
      if (!isArray(replacer)) replacer = function (key, value) {
        if (isCallable($replacer)) value = call($replacer, this, key, value);
        if (!isSymbol(value)) return value;
      };
      args[1] = replacer;
      return apply($stringify, null, args);
    }
  });
}

// `Symbol.prototype[@@toPrimitive]` method
// https://tc39.es/ecma262/#sec-symbol.prototype-@@toprimitive
if (!SymbolPrototype[TO_PRIMITIVE]) {
  var valueOf = SymbolPrototype.valueOf;
  // eslint-disable-next-line no-unused-vars -- required for .length
  redefine(SymbolPrototype, TO_PRIMITIVE, function (hint) {
    // TODO: improve hint logic
    return call(valueOf, this);
  });
}
// `Symbol.prototype[@@toStringTag]` property
// https://tc39.es/ecma262/#sec-symbol.prototype-@@tostringtag
setToStringTag($Symbol, SYMBOL);

hiddenKeys[HIDDEN] = true;


/***/ }),

/***/ 5597:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var defineWellKnownSymbol = __webpack_require__(46042);

// `Symbol.toPrimitive` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.toprimitive
defineWellKnownSymbol('toPrimitive');


/***/ }),

/***/ 38178:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var defineWellKnownSymbol = __webpack_require__(46042);

// `Symbol.toStringTag` well-known symbol
// https://tc39.es/ecma262/#sec-symbol.tostringtag
defineWellKnownSymbol('toStringTag');


/***/ }),

/***/ 73663:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var uncurryThis = __webpack_require__(38347);
var ArrayBufferViewCore = __webpack_require__(59754);
var $ArrayCopyWithin = __webpack_require__(92642);

var u$ArrayCopyWithin = uncurryThis($ArrayCopyWithin);
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.copyWithin` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.copywithin
exportTypedArrayMethod('copyWithin', function copyWithin(target, start /* , end */) {
  return u$ArrayCopyWithin(aTypedArray(this), target, start, arguments.length > 2 ? arguments[2] : undefined);
});


/***/ }),

/***/ 29883:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $every = (__webpack_require__(91102).every);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.every` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.every
exportTypedArrayMethod('every', function every(callbackfn /* , thisArg */) {
  return $every(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 35471:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var call = __webpack_require__(2834);
var $fill = __webpack_require__(72864);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.fill` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.fill
exportTypedArrayMethod('fill', function fill(value /* , start, end */) {
  var length = arguments.length;
  return call(
    $fill,
    aTypedArray(this),
    value,
    length > 1 ? arguments[1] : undefined,
    length > 2 ? arguments[2] : undefined
  );
});


/***/ }),

/***/ 21012:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $filter = (__webpack_require__(91102).filter);
var fromSpeciesAndList = __webpack_require__(59610);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.filter` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.filter
exportTypedArrayMethod('filter', function filter(callbackfn /* , thisArg */) {
  var list = $filter(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
  return fromSpeciesAndList(this, list);
});


/***/ }),

/***/ 97464:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $findIndex = (__webpack_require__(91102).findIndex);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.findIndex` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.findindex
exportTypedArrayMethod('findIndex', function findIndex(predicate /* , thisArg */) {
  return $findIndex(aTypedArray(this), predicate, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 88997:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $find = (__webpack_require__(91102).find);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.find` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.find
exportTypedArrayMethod('find', function find(predicate /* , thisArg */) {
  return $find(aTypedArray(this), predicate, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 3131:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Float32Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Float32', function (init) {
  return function Float32Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 90868:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Float64Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Float64', function (init) {
  return function Float64Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 2857:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $forEach = (__webpack_require__(91102).forEach);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.forEach` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.foreach
exportTypedArrayMethod('forEach', function forEach(callbackfn /* , thisArg */) {
  $forEach(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 83326:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS = __webpack_require__(28834);
var exportTypedArrayStaticMethod = (__webpack_require__(59754).exportTypedArrayStaticMethod);
var typedArrayFrom = __webpack_require__(83590);

// `%TypedArray%.from` method
// https://tc39.es/ecma262/#sec-%typedarray%.from
exportTypedArrayStaticMethod('from', typedArrayFrom, TYPED_ARRAYS_CONSTRUCTORS_REQUIRES_WRAPPERS);


/***/ }),

/***/ 94715:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $includes = (__webpack_require__(12636).includes);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.includes` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.includes
exportTypedArrayMethod('includes', function includes(searchElement /* , fromIndex */) {
  return $includes(aTypedArray(this), searchElement, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 13624:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $indexOf = (__webpack_require__(12636).indexOf);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.indexOf` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.indexof
exportTypedArrayMethod('indexOf', function indexOf(searchElement /* , fromIndex */) {
  return $indexOf(aTypedArray(this), searchElement, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 75626:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Int16Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Int16', function (init) {
  return function Int16Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 95756:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Int32Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Int32', function (init) {
  return function Int32Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 65553:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Int8Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Int8', function (init) {
  return function Int8Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 91132:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var PROPER_FUNCTION_NAME = (__webpack_require__(7081).PROPER);
var ArrayBufferViewCore = __webpack_require__(59754);
var ArrayIterators = __webpack_require__(81755);
var wellKnownSymbol = __webpack_require__(38688);

var ITERATOR = wellKnownSymbol('iterator');
var Uint8Array = global.Uint8Array;
var arrayValues = uncurryThis(ArrayIterators.values);
var arrayKeys = uncurryThis(ArrayIterators.keys);
var arrayEntries = uncurryThis(ArrayIterators.entries);
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var nativeTypedArrayIterator = Uint8Array && Uint8Array.prototype[ITERATOR];

var PROPER_ARRAY_VALUES_NAME = !!nativeTypedArrayIterator && nativeTypedArrayIterator.name === 'values';

var typedArrayValues = function values() {
  return arrayValues(aTypedArray(this));
};

// `%TypedArray%.prototype.entries` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.entries
exportTypedArrayMethod('entries', function entries() {
  return arrayEntries(aTypedArray(this));
});
// `%TypedArray%.prototype.keys` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.keys
exportTypedArrayMethod('keys', function keys() {
  return arrayKeys(aTypedArray(this));
});
// `%TypedArray%.prototype.values` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.values
exportTypedArrayMethod('values', typedArrayValues, PROPER_FUNCTION_NAME && !PROPER_ARRAY_VALUES_NAME);
// `%TypedArray%.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype-@@iterator
exportTypedArrayMethod(ITERATOR, typedArrayValues, PROPER_FUNCTION_NAME && !PROPER_ARRAY_VALUES_NAME);


/***/ }),

/***/ 62514:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var uncurryThis = __webpack_require__(38347);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $join = uncurryThis([].join);

// `%TypedArray%.prototype.join` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.join
exportTypedArrayMethod('join', function join(separator) {
  return $join(aTypedArray(this), separator);
});


/***/ }),

/***/ 24597:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var apply = __webpack_require__(58448);
var $lastIndexOf = __webpack_require__(84320);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.lastIndexOf` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.lastindexof
exportTypedArrayMethod('lastIndexOf', function lastIndexOf(searchElement /* , fromIndex */) {
  var length = arguments.length;
  return apply($lastIndexOf, aTypedArray(this), length > 1 ? [searchElement, arguments[1]] : [searchElement]);
});


/***/ }),

/***/ 88042:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $map = (__webpack_require__(91102).map);
var typedArraySpeciesConstructor = __webpack_require__(34815);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.map` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.map
exportTypedArrayMethod('map', function map(mapfn /* , thisArg */) {
  return $map(aTypedArray(this), mapfn, arguments.length > 1 ? arguments[1] : undefined, function (O, length) {
    return new (typedArraySpeciesConstructor(O))(length);
  });
});


/***/ }),

/***/ 92451:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $reduceRight = (__webpack_require__(32843).right);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.reduceRicht` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.reduceright
exportTypedArrayMethod('reduceRight', function reduceRight(callbackfn /* , initialValue */) {
  var length = arguments.length;
  return $reduceRight(aTypedArray(this), callbackfn, length, length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 4660:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $reduce = (__webpack_require__(32843).left);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.reduce` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.reduce
exportTypedArrayMethod('reduce', function reduce(callbackfn /* , initialValue */) {
  var length = arguments.length;
  return $reduce(aTypedArray(this), callbackfn, length, length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 44206:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var floor = Math.floor;

// `%TypedArray%.prototype.reverse` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.reverse
exportTypedArrayMethod('reverse', function reverse() {
  var that = this;
  var length = aTypedArray(that).length;
  var middle = floor(length / 2);
  var index = 0;
  var value;
  while (index < middle) {
    value = that[index];
    that[index++] = that[--length];
    that[length] = value;
  } return that;
});


/***/ }),

/***/ 66288:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var ArrayBufferViewCore = __webpack_require__(59754);
var lengthOfArrayLike = __webpack_require__(45495);
var toOffset = __webpack_require__(80670);
var toObject = __webpack_require__(43162);
var fails = __webpack_require__(47044);

var RangeError = global.RangeError;
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

var FORCED = fails(function () {
  // eslint-disable-next-line es/no-typed-arrays -- required for testing
  new Int8Array(1).set({});
});

// `%TypedArray%.prototype.set` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.set
exportTypedArrayMethod('set', function set(arrayLike /* , offset */) {
  aTypedArray(this);
  var offset = toOffset(arguments.length > 1 ? arguments[1] : undefined, 1);
  var length = this.length;
  var src = toObject(arrayLike);
  var len = lengthOfArrayLike(src);
  var index = 0;
  if (len + offset > length) throw RangeError('Wrong length');
  while (index < len) this[offset + index] = src[index++];
}, FORCED);


/***/ }),

/***/ 13250:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var typedArraySpeciesConstructor = __webpack_require__(34815);
var fails = __webpack_require__(47044);
var arraySlice = __webpack_require__(73163);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

var FORCED = fails(function () {
  // eslint-disable-next-line es/no-typed-arrays -- required for testing
  new Int8Array(1).slice();
});

// `%TypedArray%.prototype.slice` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.slice
exportTypedArrayMethod('slice', function slice(start, end) {
  var list = arraySlice(aTypedArray(this), start, end);
  var C = typedArraySpeciesConstructor(this);
  var index = 0;
  var length = list.length;
  var result = new C(length);
  while (length > index) result[index] = list[index++];
  return result;
}, FORCED);


/***/ }),

/***/ 3858:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var $some = (__webpack_require__(91102).some);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.some` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.some
exportTypedArrayMethod('some', function some(callbackfn /* , thisArg */) {
  return $some(aTypedArray(this), callbackfn, arguments.length > 1 ? arguments[1] : undefined);
});


/***/ }),

/***/ 84538:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);
var fails = __webpack_require__(47044);
var aCallable = __webpack_require__(32631);
var internalSort = __webpack_require__(43977);
var ArrayBufferViewCore = __webpack_require__(59754);
var FF = __webpack_require__(3809);
var IE_OR_EDGE = __webpack_require__(21983);
var V8 = __webpack_require__(70091);
var WEBKIT = __webpack_require__(41731);

var Array = global.Array;
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var Uint16Array = global.Uint16Array;
var un$Sort = Uint16Array && uncurryThis(Uint16Array.prototype.sort);

// WebKit
var ACCEPT_INCORRECT_ARGUMENTS = !!un$Sort && !(fails(function () {
  un$Sort(new Uint16Array(2), null);
}) && fails(function () {
  un$Sort(new Uint16Array(2), {});
}));

var STABLE_SORT = !!un$Sort && !fails(function () {
  // feature detection can be too slow, so check engines versions
  if (V8) return V8 < 74;
  if (FF) return FF < 67;
  if (IE_OR_EDGE) return true;
  if (WEBKIT) return WEBKIT < 602;

  var array = new Uint16Array(516);
  var expected = Array(516);
  var index, mod;

  for (index = 0; index < 516; index++) {
    mod = index % 4;
    array[index] = 515 - index;
    expected[index] = index - 2 * mod + 3;
  }

  un$Sort(array, function (a, b) {
    return (a / 4 | 0) - (b / 4 | 0);
  });

  for (index = 0; index < 516; index++) {
    if (array[index] !== expected[index]) return true;
  }
});

var getSortCompare = function (comparefn) {
  return function (x, y) {
    if (comparefn !== undefined) return +comparefn(x, y) || 0;
    // eslint-disable-next-line no-self-compare -- NaN check
    if (y !== y) return -1;
    // eslint-disable-next-line no-self-compare -- NaN check
    if (x !== x) return 1;
    if (x === 0 && y === 0) return 1 / x > 0 && 1 / y < 0 ? 1 : -1;
    return x > y;
  };
};

// `%TypedArray%.prototype.sort` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.sort
exportTypedArrayMethod('sort', function sort(comparefn) {
  if (comparefn !== undefined) aCallable(comparefn);
  if (STABLE_SORT) return un$Sort(this, comparefn);

  return internalSort(aTypedArray(this), getSortCompare(comparefn));
}, !STABLE_SORT || ACCEPT_INCORRECT_ARGUMENTS);


/***/ }),

/***/ 64793:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var ArrayBufferViewCore = __webpack_require__(59754);
var toLength = __webpack_require__(23417);
var toAbsoluteIndex = __webpack_require__(74841);
var typedArraySpeciesConstructor = __webpack_require__(34815);

var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;

// `%TypedArray%.prototype.subarray` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.subarray
exportTypedArrayMethod('subarray', function subarray(begin, end) {
  var O = aTypedArray(this);
  var length = O.length;
  var beginIndex = toAbsoluteIndex(begin, length);
  var C = typedArraySpeciesConstructor(O);
  return new C(
    O.buffer,
    O.byteOffset + beginIndex * O.BYTES_PER_ELEMENT,
    toLength((end === undefined ? length : toAbsoluteIndex(end, length)) - beginIndex)
  );
});


/***/ }),

/***/ 74202:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var global = __webpack_require__(32010);
var apply = __webpack_require__(58448);
var ArrayBufferViewCore = __webpack_require__(59754);
var fails = __webpack_require__(47044);
var arraySlice = __webpack_require__(73163);

var Int8Array = global.Int8Array;
var aTypedArray = ArrayBufferViewCore.aTypedArray;
var exportTypedArrayMethod = ArrayBufferViewCore.exportTypedArrayMethod;
var $toLocaleString = [].toLocaleString;

// iOS Safari 6.x fails here
var TO_LOCALE_STRING_BUG = !!Int8Array && fails(function () {
  $toLocaleString.call(new Int8Array(1));
});

var FORCED = fails(function () {
  return [1, 2].toLocaleString() != new Int8Array([1, 2]).toLocaleString();
}) || !fails(function () {
  Int8Array.prototype.toLocaleString.call([1, 2]);
});

// `%TypedArray%.prototype.toLocaleString` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.tolocalestring
exportTypedArrayMethod('toLocaleString', function toLocaleString() {
  return apply(
    $toLocaleString,
    TO_LOCALE_STRING_BUG ? arraySlice(aTypedArray(this)) : aTypedArray(this),
    arraySlice(arguments)
  );
}, FORCED);


/***/ }),

/***/ 52529:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var exportTypedArrayMethod = (__webpack_require__(59754).exportTypedArrayMethod);
var fails = __webpack_require__(47044);
var global = __webpack_require__(32010);
var uncurryThis = __webpack_require__(38347);

var Uint8Array = global.Uint8Array;
var Uint8ArrayPrototype = Uint8Array && Uint8Array.prototype || {};
var arrayToString = [].toString;
var join = uncurryThis([].join);

if (fails(function () { arrayToString.call({}); })) {
  arrayToString = function toString() {
    return join(this);
  };
}

var IS_NOT_ARRAY_METHOD = Uint8ArrayPrototype.toString != arrayToString;

// `%TypedArray%.prototype.toString` method
// https://tc39.es/ecma262/#sec-%typedarray%.prototype.tostring
exportTypedArrayMethod('toString', arrayToString, IS_NOT_ARRAY_METHOD);


/***/ }),

/***/ 47969:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Uint16Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint16', function (init) {
  return function Uint16Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 59735:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Uint32Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint32', function (init) {
  return function Uint32Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 56912:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Uint8Array` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint8', function (init) {
  return function Uint8Array(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
});


/***/ }),

/***/ 58099:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var createTypedArrayConstructor = __webpack_require__(98828);

// `Uint8ClampedArray` constructor
// https://tc39.es/ecma262/#sec-typedarray-objects
createTypedArrayConstructor('Uint8', function (init) {
  return function Uint8ClampedArray(data, byteOffset, length) {
    return init(this, data, byteOffset, length);
  };
}, true);


/***/ }),

/***/ 84151:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(94910);


/***/ }),

/***/ 49109:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(64384);


/***/ }),

/***/ 98443:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(77074);


/***/ }),

/***/ 67858:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(44455);


/***/ }),

/***/ 49261:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var $ = __webpack_require__(56475);
var newPromiseCapabilityModule = __webpack_require__(56614);
var perform = __webpack_require__(61900);

// `Promise.try` method
// https://github.com/tc39/proposal-promise-try
$({ target: 'Promise', stat: true }, {
  'try': function (callbackfn) {
    var promiseCapability = newPromiseCapabilityModule.f(this);
    var result = perform(callbackfn);
    (result.error ? promiseCapability.reject : promiseCapability.resolve)(result.value);
    return promiseCapability.promise;
  }
});


/***/ }),

/***/ 1083:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(71768);


/***/ }),

/***/ 64654:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

// TODO: Remove from `core-js@4`
__webpack_require__(1593);


/***/ }),

/***/ 42437:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var DOMIterables = __webpack_require__(23327);
var DOMTokenListPrototype = __webpack_require__(67797);
var forEach = __webpack_require__(82938);
var createNonEnumerableProperty = __webpack_require__(48914);

var handlePrototype = function (CollectionPrototype) {
  // some Chrome versions have non-configurable methods on DOMTokenList
  if (CollectionPrototype && CollectionPrototype.forEach !== forEach) try {
    createNonEnumerableProperty(CollectionPrototype, 'forEach', forEach);
  } catch (error) {
    CollectionPrototype.forEach = forEach;
  }
};

for (var COLLECTION_NAME in DOMIterables) {
  if (DOMIterables[COLLECTION_NAME]) {
    handlePrototype(global[COLLECTION_NAME] && global[COLLECTION_NAME].prototype);
  }
}

handlePrototype(DOMTokenListPrototype);


/***/ }),

/***/ 94712:
/***/ (function(__unused_webpack_module, __unused_webpack_exports, __webpack_require__) {

var global = __webpack_require__(32010);
var DOMIterables = __webpack_require__(23327);
var DOMTokenListPrototype = __webpack_require__(67797);
var ArrayIteratorMethods = __webpack_require__(81755);
var createNonEnumerableProperty = __webpack_require__(48914);
var wellKnownSymbol = __webpack_require__(38688);

var ITERATOR = wellKnownSymbol('iterator');
var TO_STRING_TAG = wellKnownSymbol('toStringTag');
var ArrayValues = ArrayIteratorMethods.values;

var handlePrototype = function (CollectionPrototype, COLLECTION_NAME) {
  if (CollectionPrototype) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype[ITERATOR] !== ArrayValues) try {
      createNonEnumerableProperty(CollectionPrototype, ITERATOR, ArrayValues);
    } catch (error) {
      CollectionPrototype[ITERATOR] = ArrayValues;
    }
    if (!CollectionPrototype[TO_STRING_TAG]) {
      createNonEnumerableProperty(CollectionPrototype, TO_STRING_TAG, COLLECTION_NAME);
    }
    if (DOMIterables[COLLECTION_NAME]) for (var METHOD_NAME in ArrayIteratorMethods) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype[METHOD_NAME] !== ArrayIteratorMethods[METHOD_NAME]) try {
        createNonEnumerableProperty(CollectionPrototype, METHOD_NAME, ArrayIteratorMethods[METHOD_NAME]);
      } catch (error) {
        CollectionPrototype[METHOD_NAME] = ArrayIteratorMethods[METHOD_NAME];
      }
    }
  }
};

for (var COLLECTION_NAME in DOMIterables) {
  handlePrototype(global[COLLECTION_NAME] && global[COLLECTION_NAME].prototype, COLLECTION_NAME);
}

handlePrototype(DOMTokenListPrototype, 'DOMTokenList');


/***/ }),

/***/ 90780:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var parent = __webpack_require__(42075);
__webpack_require__(94712);

module.exports = parent;


/***/ }),

/***/ 45728:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var objectKeys = __webpack_require__(35643);
var isArguments = __webpack_require__(67906);
var is = __webpack_require__(98527);
var isRegex = __webpack_require__(71689);
var flags = __webpack_require__(64607);
var isDate = __webpack_require__(71230);

var getTime = Date.prototype.getTime;

function deepEqual(actual, expected, options) {
  var opts = options || {};

  // 7.1. All identical values are equivalent, as determined by ===.
  if (opts.strict ? is(actual, expected) : actual === expected) {
    return true;
  }

  // 7.3. Other pairs that do not both pass typeof value == 'object', equivalence is determined by ==.
  if (!actual || !expected || (typeof actual !== 'object' && typeof expected !== 'object')) {
    return opts.strict ? is(actual, expected) : actual == expected;
  }

  /*
   * 7.4. For all other Object pairs, including Array objects, equivalence is
   * determined by having the same number of owned properties (as verified
   * with Object.prototype.hasOwnProperty.call), the same set of keys
   * (although not necessarily the same order), equivalent values for every
   * corresponding key, and an identical 'prototype' property. Note: this
   * accounts for both named and indexed properties on Arrays.
   */
  // eslint-disable-next-line no-use-before-define
  return objEquiv(actual, expected, opts);
}

function isUndefinedOrNull(value) {
  return value === null || value === undefined;
}

function isBuffer(x) {
  if (!x || typeof x !== 'object' || typeof x.length !== 'number') {
    return false;
  }
  if (typeof x.copy !== 'function' || typeof x.slice !== 'function') {
    return false;
  }
  if (x.length > 0 && typeof x[0] !== 'number') {
    return false;
  }
  return true;
}

function objEquiv(a, b, opts) {
  /* eslint max-statements: [2, 50] */
  var i, key;
  if (typeof a !== typeof b) { return false; }
  if (isUndefinedOrNull(a) || isUndefinedOrNull(b)) { return false; }

  // an identical 'prototype' property.
  if (a.prototype !== b.prototype) { return false; }

  if (isArguments(a) !== isArguments(b)) { return false; }

  var aIsRegex = isRegex(a);
  var bIsRegex = isRegex(b);
  if (aIsRegex !== bIsRegex) { return false; }
  if (aIsRegex || bIsRegex) {
    return a.source === b.source && flags(a) === flags(b);
  }

  if (isDate(a) && isDate(b)) {
    return getTime.call(a) === getTime.call(b);
  }

  var aIsBuffer = isBuffer(a);
  var bIsBuffer = isBuffer(b);
  if (aIsBuffer !== bIsBuffer) { return false; }
  if (aIsBuffer || bIsBuffer) { // && would work too, because both are true or both false here
    if (a.length !== b.length) { return false; }
    for (i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) { return false; }
    }
    return true;
  }

  if (typeof a !== typeof b) { return false; }

  try {
    var ka = objectKeys(a);
    var kb = objectKeys(b);
  } catch (e) { // happens when one is a string literal and the other isn't
    return false;
  }
  // having the same number of owned properties (keys incorporates hasOwnProperty)
  if (ka.length !== kb.length) { return false; }

  // the same set of keys (although not necessarily the same order),
  ka.sort();
  kb.sort();
  // ~~~cheap key test
  for (i = ka.length - 1; i >= 0; i--) {
    if (ka[i] != kb[i]) { return false; }
  }
  // equivalent values for every corresponding key, and ~~~possibly expensive deep test
  for (i = ka.length - 1; i >= 0; i--) {
    key = ka[i];
    if (!deepEqual(a[key], b[key], opts)) { return false; }
  }

  return true;
}

module.exports = deepEqual;


/***/ }),

/***/ 89295:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var $defineProperty = __webpack_require__(56649);

var $SyntaxError = __webpack_require__(57770);
var $TypeError = __webpack_require__(96785);

var gopd = __webpack_require__(68109);

/** @type {import('.')} */
module.exports = function defineDataProperty(
	obj,
	property,
	value
) {
	if (!obj || (typeof obj !== 'object' && typeof obj !== 'function')) {
		throw new $TypeError('`obj` must be an object or a function`');
	}
	if (typeof property !== 'string' && typeof property !== 'symbol') {
		throw new $TypeError('`property` must be a string or a symbol`');
	}
	if (arguments.length > 3 && typeof arguments[3] !== 'boolean' && arguments[3] !== null) {
		throw new $TypeError('`nonEnumerable`, if provided, must be a boolean or null');
	}
	if (arguments.length > 4 && typeof arguments[4] !== 'boolean' && arguments[4] !== null) {
		throw new $TypeError('`nonWritable`, if provided, must be a boolean or null');
	}
	if (arguments.length > 5 && typeof arguments[5] !== 'boolean' && arguments[5] !== null) {
		throw new $TypeError('`nonConfigurable`, if provided, must be a boolean or null');
	}
	if (arguments.length > 6 && typeof arguments[6] !== 'boolean') {
		throw new $TypeError('`loose`, if provided, must be a boolean');
	}

	var nonEnumerable = arguments.length > 3 ? arguments[3] : null;
	var nonWritable = arguments.length > 4 ? arguments[4] : null;
	var nonConfigurable = arguments.length > 5 ? arguments[5] : null;
	var loose = arguments.length > 6 ? arguments[6] : false;

	/* @type {false | TypedPropertyDescriptor<unknown>} */
	var desc = !!gopd && gopd(obj, property);

	if ($defineProperty) {
		$defineProperty(obj, property, {
			configurable: nonConfigurable === null && desc ? desc.configurable : !nonConfigurable,
			enumerable: nonEnumerable === null && desc ? desc.enumerable : !nonEnumerable,
			value: value,
			writable: nonWritable === null && desc ? desc.writable : !nonWritable
		});
	} else if (loose || (!nonEnumerable && !nonWritable && !nonConfigurable)) {
		// must fall back to [[Set]], and was not explicitly asked to make non-enumerable, non-writable, or non-configurable
		obj[property] = value; // eslint-disable-line no-param-reassign
	} else {
		throw new $SyntaxError('This environment does not support defining a property as non-configurable, non-writable, or non-enumerable.');
	}
};


/***/ }),

/***/ 77802:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var keys = __webpack_require__(35643);
var hasSymbols = typeof Symbol === 'function' && typeof Symbol('foo') === 'symbol';

var toStr = Object.prototype.toString;
var concat = Array.prototype.concat;
var defineDataProperty = __webpack_require__(89295);

var isFunction = function (fn) {
	return typeof fn === 'function' && toStr.call(fn) === '[object Function]';
};

var supportsDescriptors = __webpack_require__(18890)();

var defineProperty = function (object, name, value, predicate) {
	if (name in object) {
		if (predicate === true) {
			if (object[name] === value) {
				return;
			}
		} else if (!isFunction(predicate) || !predicate()) {
			return;
		}
	}

	if (supportsDescriptors) {
		defineDataProperty(object, name, value, true);
	} else {
		defineDataProperty(object, name, value);
	}
};

var defineProperties = function (object, map) {
	var predicates = arguments.length > 2 ? arguments[2] : {};
	var props = keys(map);
	if (hasSymbols) {
		props = concat.call(props, Object.getOwnPropertySymbols(map));
	}
	for (var i = 0; i < props.length; i += 1) {
		defineProperty(object, props[i], map[props[i]], predicates[props[i]]);
	}
};

defineProperties.supportsDescriptors = !!supportsDescriptors;

module.exports = defineProperties;


/***/ }),

/***/ 89302:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var callBind = __webpack_require__(36688);
var gOPD = __webpack_require__(68109);

var hasProtoAccessor;
try {
	// eslint-disable-next-line no-extra-parens, no-proto
	hasProtoAccessor = /** @type {{ __proto__?: typeof Array.prototype }} */ ([]).__proto__ === Array.prototype;
} catch (e) {
	if (!e || typeof e !== 'object' || !('code' in e) || e.code !== 'ERR_PROTO_ACCESS') {
		throw e;
	}
}

// eslint-disable-next-line no-extra-parens
var desc = !!hasProtoAccessor && gOPD && gOPD(Object.prototype, /** @type {keyof typeof Object.prototype} */ ('__proto__'));

var $Object = Object;
var $getPrototypeOf = $Object.getPrototypeOf;

/** @type {import('./get')} */
module.exports = desc && typeof desc.get === 'function'
	? callBind([desc.get])
	: typeof $getPrototypeOf === 'function'
		? /** @type {import('./get')} */ function getDunder(value) {
			// eslint-disable-next-line eqeqeq
			return $getPrototypeOf(value == null ? value : $Object(value));
		}
		: false;


/***/ }),

/***/ 56649:
/***/ (function(module) {

"use strict";


/** @type {import('.')} */
var $defineProperty = Object.defineProperty || false;
if ($defineProperty) {
	try {
		$defineProperty({}, 'a', { value: 1 });
	} catch (e) {
		// IE 8 has a broken defineProperty
		$defineProperty = false;
	}
}

module.exports = $defineProperty;


/***/ }),

/***/ 29055:
/***/ (function(module) {

"use strict";


/** @type {import('./eval')} */
module.exports = EvalError;


/***/ }),

/***/ 15293:
/***/ (function(module) {

"use strict";


/** @type {import('.')} */
module.exports = Error;


/***/ }),

/***/ 18888:
/***/ (function(module) {

"use strict";


/** @type {import('./range')} */
module.exports = RangeError;


/***/ }),

/***/ 47900:
/***/ (function(module) {

"use strict";


/** @type {import('./ref')} */
module.exports = ReferenceError;


/***/ }),

/***/ 57770:
/***/ (function(module) {

"use strict";


/** @type {import('./syntax')} */
module.exports = SyntaxError;


/***/ }),

/***/ 96785:
/***/ (function(module) {

"use strict";


/** @type {import('./type')} */
module.exports = TypeError;


/***/ }),

/***/ 54055:
/***/ (function(module) {

"use strict";


/** @type {import('./uri')} */
module.exports = URIError;


/***/ }),

/***/ 75846:
/***/ (function(module) {

"use strict";


/** @type {import('.')} */
module.exports = Object;


/***/ }),

/***/ 64785:
/***/ (function(module) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



var R = typeof Reflect === 'object' ? Reflect : null
var ReflectApply = R && typeof R.apply === 'function'
  ? R.apply
  : function ReflectApply(target, receiver, args) {
    return Function.prototype.apply.call(target, receiver, args);
  }

var ReflectOwnKeys
if (R && typeof R.ownKeys === 'function') {
  ReflectOwnKeys = R.ownKeys
} else if (Object.getOwnPropertySymbols) {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target)
      .concat(Object.getOwnPropertySymbols(target));
  };
} else {
  ReflectOwnKeys = function ReflectOwnKeys(target) {
    return Object.getOwnPropertyNames(target);
  };
}

function ProcessEmitWarning(warning) {
  if (console && console.warn) console.warn(warning);
}

var NumberIsNaN = Number.isNaN || function NumberIsNaN(value) {
  return value !== value;
}

function EventEmitter() {
  EventEmitter.init.call(this);
}
module.exports = EventEmitter;
module.exports.once = once;

// Backwards-compat with node 0.10.x
EventEmitter.EventEmitter = EventEmitter;

EventEmitter.prototype._events = undefined;
EventEmitter.prototype._eventsCount = 0;
EventEmitter.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
var defaultMaxListeners = 10;

function checkListener(listener) {
  if (typeof listener !== 'function') {
    throw new TypeError('The "listener" argument must be of type Function. Received type ' + typeof listener);
  }
}

Object.defineProperty(EventEmitter, 'defaultMaxListeners', {
  enumerable: true,
  get: function() {
    return defaultMaxListeners;
  },
  set: function(arg) {
    if (typeof arg !== 'number' || arg < 0 || NumberIsNaN(arg)) {
      throw new RangeError('The value of "defaultMaxListeners" is out of range. It must be a non-negative number. Received ' + arg + '.');
    }
    defaultMaxListeners = arg;
  }
});

EventEmitter.init = function() {

  if (this._events === undefined ||
      this._events === Object.getPrototypeOf(this)._events) {
    this._events = Object.create(null);
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || NumberIsNaN(n)) {
    throw new RangeError('The value of "n" is out of range. It must be a non-negative number. Received ' + n + '.');
  }
  this._maxListeners = n;
  return this;
};

function _getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter.prototype.getMaxListeners = function getMaxListeners() {
  return _getMaxListeners(this);
};

EventEmitter.prototype.emit = function emit(type) {
  var args = [];
  for (var i = 1; i < arguments.length; i++) args.push(arguments[i]);
  var doError = (type === 'error');

  var events = this._events;
  if (events !== undefined)
    doError = (doError && events.error === undefined);
  else if (!doError)
    return false;

  // If there is no 'error' event listener then throw.
  if (doError) {
    var er;
    if (args.length > 0)
      er = args[0];
    if (er instanceof Error) {
      // Note: The comments on the `throw` lines are intentional, they show
      // up in Node's output if this results in an unhandled exception.
      throw er; // Unhandled 'error' event
    }
    // At least give some kind of context to the user
    var err = new Error('Unhandled error.' + (er ? ' (' + er.message + ')' : ''));
    err.context = er;
    throw err; // Unhandled 'error' event
  }

  var handler = events[type];

  if (handler === undefined)
    return false;

  if (typeof handler === 'function') {
    ReflectApply(handler, this, args);
  } else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      ReflectApply(listeners[i], this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  checkListener(listener);

  events = target._events;
  if (events === undefined) {
    events = target._events = Object.create(null);
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener !== undefined) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (existing === undefined) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] =
        prepend ? [listener, existing] : [existing, listener];
      // If we've already got an array, just append.
    } else if (prepend) {
      existing.unshift(listener);
    } else {
      existing.push(listener);
    }

    // Check for listener leak
    m = _getMaxListeners(target);
    if (m > 0 && existing.length > m && !existing.warned) {
      existing.warned = true;
      // No error code for this since it is a Warning
      // eslint-disable-next-line no-restricted-syntax
      var w = new Error('Possible EventEmitter memory leak detected. ' +
                          existing.length + ' ' + String(type) + ' listeners ' +
                          'added. Use emitter.setMaxListeners() to ' +
                          'increase limit');
      w.name = 'MaxListenersExceededWarning';
      w.emitter = target;
      w.type = type;
      w.count = existing.length;
      ProcessEmitWarning(w);
    }
  }

  return target;
}

EventEmitter.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function onceWrapper() {
  if (!this.fired) {
    this.target.removeListener(this.type, this.wrapFn);
    this.fired = true;
    if (arguments.length === 0)
      return this.listener.call(this.target);
    return this.listener.apply(this.target, arguments);
  }
}

function _onceWrap(target, type, listener) {
  var state = { fired: false, wrapFn: undefined, target: target, type: type, listener: listener };
  var wrapped = onceWrapper.bind(state);
  wrapped.listener = listener;
  state.wrapFn = wrapped;
  return wrapped;
}

EventEmitter.prototype.once = function once(type, listener) {
  checkListener(listener);
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      checkListener(listener);
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// Emits a 'removeListener' event if and only if the listener was removed.
EventEmitter.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      checkListener(listener);

      events = this._events;
      if (events === undefined)
        return this;

      list = events[type];
      if (list === undefined)
        return this;

      if (list === listener || list.listener === listener) {
        if (--this._eventsCount === 0)
          this._events = Object.create(null);
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length - 1; i >= 0; i--) {
          if (list[i] === listener || list[i].listener === listener) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (position === 0)
          list.shift();
        else {
          spliceOne(list, position);
        }

        if (list.length === 1)
          events[type] = list[0];

        if (events.removeListener !== undefined)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter.prototype.off = EventEmitter.prototype.removeListener;

EventEmitter.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events, i;

      events = this._events;
      if (events === undefined)
        return this;

      // not listening for removeListener, no need to emit
      if (events.removeListener === undefined) {
        if (arguments.length === 0) {
          this._events = Object.create(null);
          this._eventsCount = 0;
        } else if (events[type] !== undefined) {
          if (--this._eventsCount === 0)
            this._events = Object.create(null);
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        var key;
        for (i = 0; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = Object.create(null);
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners !== undefined) {
        // LIFO order
        for (i = listeners.length - 1; i >= 0; i--) {
          this.removeListener(type, listeners[i]);
        }
      }

      return this;
    };

function _listeners(target, type, unwrap) {
  var events = target._events;

  if (events === undefined)
    return [];

  var evlistener = events[type];
  if (evlistener === undefined)
    return [];

  if (typeof evlistener === 'function')
    return unwrap ? [evlistener.listener || evlistener] : [evlistener];

  return unwrap ?
    unwrapListeners(evlistener) : arrayClone(evlistener, evlistener.length);
}

EventEmitter.prototype.listeners = function listeners(type) {
  return _listeners(this, type, true);
};

EventEmitter.prototype.rawListeners = function rawListeners(type) {
  return _listeners(this, type, false);
};

EventEmitter.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events !== undefined) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener !== undefined) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? ReflectOwnKeys(this._events) : [];
};

function arrayClone(arr, n) {
  var copy = new Array(n);
  for (var i = 0; i < n; ++i)
    copy[i] = arr[i];
  return copy;
}

function spliceOne(list, index) {
  for (; index + 1 < list.length; index++)
    list[index] = list[index + 1];
  list.pop();
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

function once(emitter, name) {
  return new Promise(function (resolve, reject) {
    function errorListener(err) {
      emitter.removeListener(name, resolver);
      reject(err);
    }

    function resolver() {
      if (typeof emitter.removeListener === 'function') {
        emitter.removeListener('error', errorListener);
      }
      resolve([].slice.call(arguments));
    };

    eventTargetAgnosticAddListener(emitter, name, resolver, { once: true });
    if (name !== 'error') {
      addErrorHandlerIfEventEmitter(emitter, errorListener, { once: true });
    }
  });
}

function addErrorHandlerIfEventEmitter(emitter, handler, flags) {
  if (typeof emitter.on === 'function') {
    eventTargetAgnosticAddListener(emitter, 'error', handler, flags);
  }
}

function eventTargetAgnosticAddListener(emitter, name, listener, flags) {
  if (typeof emitter.on === 'function') {
    if (flags.once) {
      emitter.once(name, listener);
    } else {
      emitter.on(name, listener);
    }
  } else if (typeof emitter.addEventListener === 'function') {
    // EventTarget does not have `error` event semantics like Node
    // EventEmitters, we do not listen for `error` events here.
    emitter.addEventListener(name, function wrapListener(arg) {
      // IE does not have builtin `{ once: true }` support so we
      // have to do it manually.
      if (flags.once) {
        emitter.removeEventListener(name, wrapListener);
      }
      listener(arg);
    });
  } else {
    throw new TypeError('The "emitter" argument must be of type EventEmitter. Received type ' + typeof emitter);
  }
}


/***/ }),

/***/ 72022:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


// eslint-disable-next-line func-names
module.exports = function () {
  if (typeof globalThis === "object") {
    return globalThis;
  }
  var g;
  try {
    // This works if eval is allowed (see CSP)
    // eslint-disable-next-line no-new-func
    g = this || new Function("return this")();
  } catch (e) {
    // This works if the window reference is available
    if (typeof window === "object") {
      return window;
    }

    // This works if the self reference is available
    if (typeof self === "object") {
      return self;
    }

    // This works if the global reference is available
    if (typeof __webpack_require__.g !== "undefined") {
      return __webpack_require__.g;
    }
  }
  return g;
}();

/***/ }),

/***/ 68404:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isCallable = __webpack_require__(3746);

var toStr = Object.prototype.toString;
var hasOwnProperty = Object.prototype.hasOwnProperty;

var forEachArray = function forEachArray(array, iterator, receiver) {
    for (var i = 0, len = array.length; i < len; i++) {
        if (hasOwnProperty.call(array, i)) {
            if (receiver == null) {
                iterator(array[i], i, array);
            } else {
                iterator.call(receiver, array[i], i, array);
            }
        }
    }
};

var forEachString = function forEachString(string, iterator, receiver) {
    for (var i = 0, len = string.length; i < len; i++) {
        // no such thing as a sparse string.
        if (receiver == null) {
            iterator(string.charAt(i), i, string);
        } else {
            iterator.call(receiver, string.charAt(i), i, string);
        }
    }
};

var forEachObject = function forEachObject(object, iterator, receiver) {
    for (var k in object) {
        if (hasOwnProperty.call(object, k)) {
            if (receiver == null) {
                iterator(object[k], k, object);
            } else {
                iterator.call(receiver, object[k], k, object);
            }
        }
    }
};

var forEach = function forEach(list, iterator, thisArg) {
    if (!isCallable(iterator)) {
        throw new TypeError('iterator must be a function');
    }

    var receiver;
    if (arguments.length >= 3) {
        receiver = thisArg;
    }

    if (toStr.call(list) === '[object Array]') {
        forEachArray(list, iterator, receiver);
    } else if (typeof list === 'string') {
        forEachString(list, iterator, receiver);
    } else {
        forEachObject(list, iterator, receiver);
    }
};

module.exports = forEach;


/***/ }),

/***/ 12719:
/***/ (function(module) {

"use strict";


/* eslint no-invalid-this: 1 */

var ERROR_MESSAGE = 'Function.prototype.bind called on incompatible ';
var toStr = Object.prototype.toString;
var max = Math.max;
var funcType = '[object Function]';

var concatty = function concatty(a, b) {
    var arr = [];

    for (var i = 0; i < a.length; i += 1) {
        arr[i] = a[i];
    }
    for (var j = 0; j < b.length; j += 1) {
        arr[j + a.length] = b[j];
    }

    return arr;
};

var slicy = function slicy(arrLike, offset) {
    var arr = [];
    for (var i = offset || 0, j = 0; i < arrLike.length; i += 1, j += 1) {
        arr[j] = arrLike[i];
    }
    return arr;
};

var joiny = function (arr, joiner) {
    var str = '';
    for (var i = 0; i < arr.length; i += 1) {
        str += arr[i];
        if (i + 1 < arr.length) {
            str += joiner;
        }
    }
    return str;
};

module.exports = function bind(that) {
    var target = this;
    if (typeof target !== 'function' || toStr.apply(target) !== funcType) {
        throw new TypeError(ERROR_MESSAGE + target);
    }
    var args = slicy(arguments, 1);

    var bound;
    var binder = function () {
        if (this instanceof bound) {
            var result = target.apply(
                this,
                concatty(args, arguments)
            );
            if (Object(result) === result) {
                return result;
            }
            return this;
        }
        return target.apply(
            that,
            concatty(args, arguments)
        );

    };

    var boundLength = max(0, target.length - args.length);
    var boundArgs = [];
    for (var i = 0; i < boundLength; i++) {
        boundArgs[i] = '$' + i;
    }

    bound = Function('binder', 'return function (' + joiny(boundArgs, ',') + '){ return binder.apply(this,arguments); }')(binder);

    if (target.prototype) {
        var Empty = function Empty() {};
        Empty.prototype = target.prototype;
        bound.prototype = new Empty();
        Empty.prototype = null;
    }

    return bound;
};


/***/ }),

/***/ 5049:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(12719);

module.exports = Function.prototype.bind || implementation;


/***/ }),

/***/ 61084:
/***/ (function(module) {

"use strict";


var functionsHaveNames = function functionsHaveNames() {
	return typeof function f() {}.name === 'string';
};

var gOPD = Object.getOwnPropertyDescriptor;
if (gOPD) {
	try {
		gOPD([], 'length');
	} catch (e) {
		// IE 8 has a broken gOPD
		gOPD = null;
	}
}

functionsHaveNames.functionsHaveConfigurableNames = function functionsHaveConfigurableNames() {
	if (!functionsHaveNames() || !gOPD) {
		return false;
	}
	var desc = gOPD(function () {}, 'name');
	return !!desc && !!desc.configurable;
};

var $bind = Function.prototype.bind;

functionsHaveNames.boundFunctionsHaveNames = function boundFunctionsHaveNames() {
	return functionsHaveNames() && typeof $bind === 'function' && function f() {}.bind().name !== '';
};

module.exports = functionsHaveNames;


/***/ }),

/***/ 28651:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var undefined;

var $Object = __webpack_require__(75846);

var $Error = __webpack_require__(15293);
var $EvalError = __webpack_require__(29055);
var $RangeError = __webpack_require__(18888);
var $ReferenceError = __webpack_require__(47900);
var $SyntaxError = __webpack_require__(57770);
var $TypeError = __webpack_require__(96785);
var $URIError = __webpack_require__(54055);

var abs = __webpack_require__(50716);
var floor = __webpack_require__(77450);
var max = __webpack_require__(3774);
var min = __webpack_require__(47552);
var pow = __webpack_require__(75874);

var $Function = Function;

// eslint-disable-next-line consistent-return
var getEvalledConstructor = function (expressionSyntax) {
	try {
		return $Function('"use strict"; return (' + expressionSyntax + ').constructor;')();
	} catch (e) {}
};

var $gOPD = __webpack_require__(68109);
var $defineProperty = __webpack_require__(56649);

var throwTypeError = function () {
	throw new $TypeError();
};
var ThrowTypeError = $gOPD
	? (function () {
		try {
			// eslint-disable-next-line no-unused-expressions, no-caller, no-restricted-properties
			arguments.callee; // IE 8 does not throw here
			return throwTypeError;
		} catch (calleeThrows) {
			try {
				// IE 8 throws on Object.getOwnPropertyDescriptor(arguments, '')
				return $gOPD(arguments, 'callee').get;
			} catch (gOPDthrows) {
				return throwTypeError;
			}
		}
	}())
	: throwTypeError;

var hasSymbols = __webpack_require__(73257)();
var getDunderProto = __webpack_require__(89302);

var getProto = (typeof Reflect === 'function' && Reflect.getPrototypeOf)
	|| $Object.getPrototypeOf
	|| getDunderProto;

var $apply = __webpack_require__(73036);
var $call = __webpack_require__(10078);

var needsEval = {};

var TypedArray = typeof Uint8Array === 'undefined' || !getProto ? undefined : getProto(Uint8Array);

var INTRINSICS = {
	__proto__: null,
	'%AggregateError%': typeof AggregateError === 'undefined' ? undefined : AggregateError,
	'%Array%': Array,
	'%ArrayBuffer%': typeof ArrayBuffer === 'undefined' ? undefined : ArrayBuffer,
	'%ArrayIteratorPrototype%': hasSymbols && getProto ? getProto([][Symbol.iterator]()) : undefined,
	'%AsyncFromSyncIteratorPrototype%': undefined,
	'%AsyncFunction%': needsEval,
	'%AsyncGenerator%': needsEval,
	'%AsyncGeneratorFunction%': needsEval,
	'%AsyncIteratorPrototype%': needsEval,
	'%Atomics%': typeof Atomics === 'undefined' ? undefined : Atomics,
	'%BigInt%': typeof BigInt === 'undefined' ? undefined : BigInt,
	'%BigInt64Array%': typeof BigInt64Array === 'undefined' ? undefined : BigInt64Array,
	'%BigUint64Array%': typeof BigUint64Array === 'undefined' ? undefined : BigUint64Array,
	'%Boolean%': Boolean,
	'%DataView%': typeof DataView === 'undefined' ? undefined : DataView,
	'%Date%': Date,
	'%decodeURI%': decodeURI,
	'%decodeURIComponent%': decodeURIComponent,
	'%encodeURI%': encodeURI,
	'%encodeURIComponent%': encodeURIComponent,
	'%Error%': $Error,
	'%eval%': eval, // eslint-disable-line no-eval
	'%EvalError%': $EvalError,
	'%Float32Array%': typeof Float32Array === 'undefined' ? undefined : Float32Array,
	'%Float64Array%': typeof Float64Array === 'undefined' ? undefined : Float64Array,
	'%FinalizationRegistry%': typeof FinalizationRegistry === 'undefined' ? undefined : FinalizationRegistry,
	'%Function%': $Function,
	'%GeneratorFunction%': needsEval,
	'%Int8Array%': typeof Int8Array === 'undefined' ? undefined : Int8Array,
	'%Int16Array%': typeof Int16Array === 'undefined' ? undefined : Int16Array,
	'%Int32Array%': typeof Int32Array === 'undefined' ? undefined : Int32Array,
	'%isFinite%': isFinite,
	'%isNaN%': isNaN,
	'%IteratorPrototype%': hasSymbols && getProto ? getProto(getProto([][Symbol.iterator]())) : undefined,
	'%JSON%': typeof JSON === 'object' ? JSON : undefined,
	'%Map%': typeof Map === 'undefined' ? undefined : Map,
	'%MapIteratorPrototype%': typeof Map === 'undefined' || !hasSymbols || !getProto ? undefined : getProto(new Map()[Symbol.iterator]()),
	'%Math%': Math,
	'%Number%': Number,
	'%Object%': $Object,
	'%Object.getOwnPropertyDescriptor%': $gOPD,
	'%parseFloat%': parseFloat,
	'%parseInt%': parseInt,
	'%Promise%': typeof Promise === 'undefined' ? undefined : Promise,
	'%Proxy%': typeof Proxy === 'undefined' ? undefined : Proxy,
	'%RangeError%': $RangeError,
	'%ReferenceError%': $ReferenceError,
	'%Reflect%': typeof Reflect === 'undefined' ? undefined : Reflect,
	'%RegExp%': RegExp,
	'%Set%': typeof Set === 'undefined' ? undefined : Set,
	'%SetIteratorPrototype%': typeof Set === 'undefined' || !hasSymbols || !getProto ? undefined : getProto(new Set()[Symbol.iterator]()),
	'%SharedArrayBuffer%': typeof SharedArrayBuffer === 'undefined' ? undefined : SharedArrayBuffer,
	'%String%': String,
	'%StringIteratorPrototype%': hasSymbols && getProto ? getProto(''[Symbol.iterator]()) : undefined,
	'%Symbol%': hasSymbols ? Symbol : undefined,
	'%SyntaxError%': $SyntaxError,
	'%ThrowTypeError%': ThrowTypeError,
	'%TypedArray%': TypedArray,
	'%TypeError%': $TypeError,
	'%Uint8Array%': typeof Uint8Array === 'undefined' ? undefined : Uint8Array,
	'%Uint8ClampedArray%': typeof Uint8ClampedArray === 'undefined' ? undefined : Uint8ClampedArray,
	'%Uint16Array%': typeof Uint16Array === 'undefined' ? undefined : Uint16Array,
	'%Uint32Array%': typeof Uint32Array === 'undefined' ? undefined : Uint32Array,
	'%URIError%': $URIError,
	'%WeakMap%': typeof WeakMap === 'undefined' ? undefined : WeakMap,
	'%WeakRef%': typeof WeakRef === 'undefined' ? undefined : WeakRef,
	'%WeakSet%': typeof WeakSet === 'undefined' ? undefined : WeakSet,

	'%Function.prototype.call%': $call,
	'%Function.prototype.apply%': $apply,
	'%Object.defineProperty%': $defineProperty,
	'%Math.abs%': abs,
	'%Math.floor%': floor,
	'%Math.max%': max,
	'%Math.min%': min,
	'%Math.pow%': pow
};

if (getProto) {
	try {
		null.error; // eslint-disable-line no-unused-expressions
	} catch (e) {
		// https://github.com/tc39/proposal-shadowrealm/pull/384#issuecomment-1364264229
		var errorProto = getProto(getProto(e));
		INTRINSICS['%Error.prototype%'] = errorProto;
	}
}

var doEval = function doEval(name) {
	var value;
	if (name === '%AsyncFunction%') {
		value = getEvalledConstructor('async function () {}');
	} else if (name === '%GeneratorFunction%') {
		value = getEvalledConstructor('function* () {}');
	} else if (name === '%AsyncGeneratorFunction%') {
		value = getEvalledConstructor('async function* () {}');
	} else if (name === '%AsyncGenerator%') {
		var fn = doEval('%AsyncGeneratorFunction%');
		if (fn) {
			value = fn.prototype;
		}
	} else if (name === '%AsyncIteratorPrototype%') {
		var gen = doEval('%AsyncGenerator%');
		if (gen && getProto) {
			value = getProto(gen.prototype);
		}
	}

	INTRINSICS[name] = value;

	return value;
};

var LEGACY_ALIASES = {
	__proto__: null,
	'%ArrayBufferPrototype%': ['ArrayBuffer', 'prototype'],
	'%ArrayPrototype%': ['Array', 'prototype'],
	'%ArrayProto_entries%': ['Array', 'prototype', 'entries'],
	'%ArrayProto_forEach%': ['Array', 'prototype', 'forEach'],
	'%ArrayProto_keys%': ['Array', 'prototype', 'keys'],
	'%ArrayProto_values%': ['Array', 'prototype', 'values'],
	'%AsyncFunctionPrototype%': ['AsyncFunction', 'prototype'],
	'%AsyncGenerator%': ['AsyncGeneratorFunction', 'prototype'],
	'%AsyncGeneratorPrototype%': ['AsyncGeneratorFunction', 'prototype', 'prototype'],
	'%BooleanPrototype%': ['Boolean', 'prototype'],
	'%DataViewPrototype%': ['DataView', 'prototype'],
	'%DatePrototype%': ['Date', 'prototype'],
	'%ErrorPrototype%': ['Error', 'prototype'],
	'%EvalErrorPrototype%': ['EvalError', 'prototype'],
	'%Float32ArrayPrototype%': ['Float32Array', 'prototype'],
	'%Float64ArrayPrototype%': ['Float64Array', 'prototype'],
	'%FunctionPrototype%': ['Function', 'prototype'],
	'%Generator%': ['GeneratorFunction', 'prototype'],
	'%GeneratorPrototype%': ['GeneratorFunction', 'prototype', 'prototype'],
	'%Int8ArrayPrototype%': ['Int8Array', 'prototype'],
	'%Int16ArrayPrototype%': ['Int16Array', 'prototype'],
	'%Int32ArrayPrototype%': ['Int32Array', 'prototype'],
	'%JSONParse%': ['JSON', 'parse'],
	'%JSONStringify%': ['JSON', 'stringify'],
	'%MapPrototype%': ['Map', 'prototype'],
	'%NumberPrototype%': ['Number', 'prototype'],
	'%ObjectPrototype%': ['Object', 'prototype'],
	'%ObjProto_toString%': ['Object', 'prototype', 'toString'],
	'%ObjProto_valueOf%': ['Object', 'prototype', 'valueOf'],
	'%PromisePrototype%': ['Promise', 'prototype'],
	'%PromiseProto_then%': ['Promise', 'prototype', 'then'],
	'%Promise_all%': ['Promise', 'all'],
	'%Promise_reject%': ['Promise', 'reject'],
	'%Promise_resolve%': ['Promise', 'resolve'],
	'%RangeErrorPrototype%': ['RangeError', 'prototype'],
	'%ReferenceErrorPrototype%': ['ReferenceError', 'prototype'],
	'%RegExpPrototype%': ['RegExp', 'prototype'],
	'%SetPrototype%': ['Set', 'prototype'],
	'%SharedArrayBufferPrototype%': ['SharedArrayBuffer', 'prototype'],
	'%StringPrototype%': ['String', 'prototype'],
	'%SymbolPrototype%': ['Symbol', 'prototype'],
	'%SyntaxErrorPrototype%': ['SyntaxError', 'prototype'],
	'%TypedArrayPrototype%': ['TypedArray', 'prototype'],
	'%TypeErrorPrototype%': ['TypeError', 'prototype'],
	'%Uint8ArrayPrototype%': ['Uint8Array', 'prototype'],
	'%Uint8ClampedArrayPrototype%': ['Uint8ClampedArray', 'prototype'],
	'%Uint16ArrayPrototype%': ['Uint16Array', 'prototype'],
	'%Uint32ArrayPrototype%': ['Uint32Array', 'prototype'],
	'%URIErrorPrototype%': ['URIError', 'prototype'],
	'%WeakMapPrototype%': ['WeakMap', 'prototype'],
	'%WeakSetPrototype%': ['WeakSet', 'prototype']
};

var bind = __webpack_require__(5049);
var hasOwn = __webpack_require__(55215);
var $concat = bind.call($call, Array.prototype.concat);
var $spliceApply = bind.call($apply, Array.prototype.splice);
var $replace = bind.call($call, String.prototype.replace);
var $strSlice = bind.call($call, String.prototype.slice);
var $exec = bind.call($call, RegExp.prototype.exec);

/* adapted from https://github.com/lodash/lodash/blob/4.17.15/dist/lodash.js#L6735-L6744 */
var rePropName = /[^%.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|%$))/g;
var reEscapeChar = /\\(\\)?/g; /** Used to match backslashes in property paths. */
var stringToPath = function stringToPath(string) {
	var first = $strSlice(string, 0, 1);
	var last = $strSlice(string, -1);
	if (first === '%' && last !== '%') {
		throw new $SyntaxError('invalid intrinsic syntax, expected closing `%`');
	} else if (last === '%' && first !== '%') {
		throw new $SyntaxError('invalid intrinsic syntax, expected opening `%`');
	}
	var result = [];
	$replace(string, rePropName, function (match, number, quote, subString) {
		result[result.length] = quote ? $replace(subString, reEscapeChar, '$1') : number || match;
	});
	return result;
};
/* end adaptation */

var getBaseIntrinsic = function getBaseIntrinsic(name, allowMissing) {
	var intrinsicName = name;
	var alias;
	if (hasOwn(LEGACY_ALIASES, intrinsicName)) {
		alias = LEGACY_ALIASES[intrinsicName];
		intrinsicName = '%' + alias[0] + '%';
	}

	if (hasOwn(INTRINSICS, intrinsicName)) {
		var value = INTRINSICS[intrinsicName];
		if (value === needsEval) {
			value = doEval(intrinsicName);
		}
		if (typeof value === 'undefined' && !allowMissing) {
			throw new $TypeError('intrinsic ' + name + ' exists, but is not available. Please file an issue!');
		}

		return {
			alias: alias,
			name: intrinsicName,
			value: value
		};
	}

	throw new $SyntaxError('intrinsic ' + name + ' does not exist!');
};

module.exports = function GetIntrinsic(name, allowMissing) {
	if (typeof name !== 'string' || name.length === 0) {
		throw new $TypeError('intrinsic name must be a non-empty string');
	}
	if (arguments.length > 1 && typeof allowMissing !== 'boolean') {
		throw new $TypeError('"allowMissing" argument must be a boolean');
	}

	if ($exec(/^%?[^%]*%?$/, name) === null) {
		throw new $SyntaxError('`%` may not be present anywhere but at the beginning and end of the intrinsic name');
	}
	var parts = stringToPath(name);
	var intrinsicBaseName = parts.length > 0 ? parts[0] : '';

	var intrinsic = getBaseIntrinsic('%' + intrinsicBaseName + '%', allowMissing);
	var intrinsicRealName = intrinsic.name;
	var value = intrinsic.value;
	var skipFurtherCaching = false;

	var alias = intrinsic.alias;
	if (alias) {
		intrinsicBaseName = alias[0];
		$spliceApply(parts, $concat([0, 1], alias));
	}

	for (var i = 1, isOwn = true; i < parts.length; i += 1) {
		var part = parts[i];
		var first = $strSlice(part, 0, 1);
		var last = $strSlice(part, -1);
		if (
			(
				(first === '"' || first === "'" || first === '`')
				|| (last === '"' || last === "'" || last === '`')
			)
			&& first !== last
		) {
			throw new $SyntaxError('property names with quotes must have matching quotes');
		}
		if (part === 'constructor' || !isOwn) {
			skipFurtherCaching = true;
		}

		intrinsicBaseName += '.' + part;
		intrinsicRealName = '%' + intrinsicBaseName + '%';

		if (hasOwn(INTRINSICS, intrinsicRealName)) {
			value = INTRINSICS[intrinsicRealName];
		} else if (value != null) {
			if (!(part in value)) {
				if (!allowMissing) {
					throw new $TypeError('base intrinsic for ' + name + ' exists, but the property is not available.');
				}
				return void undefined;
			}
			if ($gOPD && (i + 1) >= parts.length) {
				var desc = $gOPD(value, part);
				isOwn = !!desc;

				// By convention, when a data property is converted to an accessor
				// property to emulate a data property that does not suffer from
				// the override mistake, that accessor's getter is marked with
				// an `originalValue` property. Here, when we detect this, we
				// uphold the illusion by pretending to see that original data
				// property, i.e., returning the value rather than the getter
				// itself.
				if (isOwn && 'get' in desc && !('originalValue' in desc.get)) {
					value = desc.get;
				} else {
					value = value[part];
				}
			} else {
				isOwn = hasOwn(value, part);
				value = value[part];
			}

			if (isOwn && !skipFurtherCaching) {
				INTRINSICS[intrinsicRealName] = value;
			}
		}
	}
	return value;
};


/***/ }),

/***/ 85567:
/***/ (function(module) {

"use strict";


/** @type {import('./gOPD')} */
module.exports = Object.getOwnPropertyDescriptor;


/***/ }),

/***/ 68109:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


/** @type {import('.')} */
var $gOPD = __webpack_require__(85567);

if ($gOPD) {
	try {
		$gOPD([], 'length');
	} catch (e) {
		// IE 8 has a broken gOPD
		$gOPD = null;
	}
}

module.exports = $gOPD;


/***/ }),

/***/ 18890:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var $defineProperty = __webpack_require__(56649);

var hasPropertyDescriptors = function hasPropertyDescriptors() {
	return !!$defineProperty;
};

hasPropertyDescriptors.hasArrayLengthDefineBug = function hasArrayLengthDefineBug() {
	// node v0.6 has a bug where array lengths can be Set but not Defined
	if (!$defineProperty) {
		return null;
	}
	try {
		return $defineProperty([], 'length', { value: 1 }).length !== 1;
	} catch (e) {
		// In Firefox 4-22, defining length on an array throws an exception.
		return true;
	}
};

module.exports = hasPropertyDescriptors;


/***/ }),

/***/ 73257:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var origSymbol = typeof Symbol !== 'undefined' && Symbol;
var hasSymbolSham = __webpack_require__(12843);

/** @type {import('.')} */
module.exports = function hasNativeSymbols() {
	if (typeof origSymbol !== 'function') { return false; }
	if (typeof Symbol !== 'function') { return false; }
	if (typeof origSymbol('foo') !== 'symbol') { return false; }
	if (typeof Symbol('bar') !== 'symbol') { return false; }

	return hasSymbolSham();
};


/***/ }),

/***/ 12843:
/***/ (function(module) {

"use strict";


/** @type {import('./shams')} */
/* eslint complexity: [2, 18], max-statements: [2, 33] */
module.exports = function hasSymbols() {
	if (typeof Symbol !== 'function' || typeof Object.getOwnPropertySymbols !== 'function') { return false; }
	if (typeof Symbol.iterator === 'symbol') { return true; }

	/** @type {{ [k in symbol]?: unknown }} */
	var obj = {};
	var sym = Symbol('test');
	var symObj = Object(sym);
	if (typeof sym === 'string') { return false; }

	if (Object.prototype.toString.call(sym) !== '[object Symbol]') { return false; }
	if (Object.prototype.toString.call(symObj) !== '[object Symbol]') { return false; }

	// temp disabled per https://github.com/ljharb/object.assign/issues/17
	// if (sym instanceof Symbol) { return false; }
	// temp disabled per https://github.com/WebReflection/get-own-property-symbols/issues/4
	// if (!(symObj instanceof Symbol)) { return false; }

	// if (typeof Symbol.prototype.toString !== 'function') { return false; }
	// if (String(sym) !== Symbol.prototype.toString.call(sym)) { return false; }

	var symVal = 42;
	obj[sym] = symVal;
	for (var _ in obj) { return false; } // eslint-disable-line no-restricted-syntax, no-unreachable-loop
	if (typeof Object.keys === 'function' && Object.keys(obj).length !== 0) { return false; }

	if (typeof Object.getOwnPropertyNames === 'function' && Object.getOwnPropertyNames(obj).length !== 0) { return false; }

	var syms = Object.getOwnPropertySymbols(obj);
	if (syms.length !== 1 || syms[0] !== sym) { return false; }

	if (!Object.prototype.propertyIsEnumerable.call(obj, sym)) { return false; }

	if (typeof Object.getOwnPropertyDescriptor === 'function') {
		// eslint-disable-next-line no-extra-parens
		var descriptor = /** @type {PropertyDescriptor} */ (Object.getOwnPropertyDescriptor(obj, sym));
		if (descriptor.value !== symVal || descriptor.enumerable !== true) { return false; }
	}

	return true;
};


/***/ }),

/***/ 26626:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var hasSymbols = __webpack_require__(12843);

/** @type {import('.')} */
module.exports = function hasToStringTagShams() {
	return hasSymbols() && !!Symbol.toStringTag;
};


/***/ }),

/***/ 55215:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var call = Function.prototype.call;
var $hasOwn = Object.prototype.hasOwnProperty;
var bind = __webpack_require__(5049);

/** @type {import('.')} */
module.exports = bind.call(call, $hasOwn);


/***/ }),

/***/ 35143:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";

var Buffer = (__webpack_require__(93143).Buffer);

// Multibyte codec. In this scheme, a character is represented by 1 or more bytes.
// Our codec supports UTF-16 surrogates, extensions for GB18030 and unicode sequences.
// To save memory and loading time, we read table files only when requested.

exports._dbcs = DBCSCodec;

var UNASSIGNED = -1,
    GB18030_CODE = -2,
    SEQ_START  = -10,
    NODE_START = -1000,
    UNASSIGNED_NODE = new Array(0x100),
    DEF_CHAR = -1;

for (var i = 0; i < 0x100; i++)
    UNASSIGNED_NODE[i] = UNASSIGNED;


// Class DBCSCodec reads and initializes mapping tables.
function DBCSCodec(codecOptions, iconv) {
    this.encodingName = codecOptions.encodingName;
    if (!codecOptions)
        throw new Error("DBCS codec is called without the data.")
    if (!codecOptions.table)
        throw new Error("Encoding '" + this.encodingName + "' has no data.");

    // Load tables.
    var mappingTable = codecOptions.table();


    // Decode tables: MBCS -> Unicode.

    // decodeTables is a trie, encoded as an array of arrays of integers. Internal arrays are trie nodes and all have len = 256.
    // Trie root is decodeTables[0].
    // Values: >=  0 -> unicode character code. can be > 0xFFFF
    //         == UNASSIGNED -> unknown/unassigned sequence.
    //         == GB18030_CODE -> this is the end of a GB18030 4-byte sequence.
    //         <= NODE_START -> index of the next node in our trie to process next byte.
    //         <= SEQ_START  -> index of the start of a character code sequence, in decodeTableSeq.
    this.decodeTables = [];
    this.decodeTables[0] = UNASSIGNED_NODE.slice(0); // Create root node.

    // Sometimes a MBCS char corresponds to a sequence of unicode chars. We store them as arrays of integers here. 
    this.decodeTableSeq = [];

    // Actual mapping tables consist of chunks. Use them to fill up decode tables.
    for (var i = 0; i < mappingTable.length; i++)
        this._addDecodeChunk(mappingTable[i]);

    // Load & create GB18030 tables when needed.
    if (typeof codecOptions.gb18030 === 'function') {
        this.gb18030 = codecOptions.gb18030(); // Load GB18030 ranges.

        // Add GB18030 common decode nodes.
        var commonThirdByteNodeIdx = this.decodeTables.length;
        this.decodeTables.push(UNASSIGNED_NODE.slice(0));

        var commonFourthByteNodeIdx = this.decodeTables.length;
        this.decodeTables.push(UNASSIGNED_NODE.slice(0));

        // Fill out the tree
        var firstByteNode = this.decodeTables[0];
        for (var i = 0x81; i <= 0xFE; i++) {
            var secondByteNode = this.decodeTables[NODE_START - firstByteNode[i]];
            for (var j = 0x30; j <= 0x39; j++) {
                if (secondByteNode[j] === UNASSIGNED) {
                    secondByteNode[j] = NODE_START - commonThirdByteNodeIdx;
                } else if (secondByteNode[j] > NODE_START) {
                    throw new Error("gb18030 decode tables conflict at byte 2");
                }

                var thirdByteNode = this.decodeTables[NODE_START - secondByteNode[j]];
                for (var k = 0x81; k <= 0xFE; k++) {
                    if (thirdByteNode[k] === UNASSIGNED) {
                        thirdByteNode[k] = NODE_START - commonFourthByteNodeIdx;
                    } else if (thirdByteNode[k] === NODE_START - commonFourthByteNodeIdx) {
                        continue;
                    } else if (thirdByteNode[k] > NODE_START) {
                        throw new Error("gb18030 decode tables conflict at byte 3");
                    }

                    var fourthByteNode = this.decodeTables[NODE_START - thirdByteNode[k]];
                    for (var l = 0x30; l <= 0x39; l++) {
                        if (fourthByteNode[l] === UNASSIGNED)
                            fourthByteNode[l] = GB18030_CODE;
                    }
                }
            }
        }
    }

    this.defaultCharUnicode = iconv.defaultCharUnicode;

    
    // Encode tables: Unicode -> DBCS.

    // `encodeTable` is array mapping from unicode char to encoded char. All its values are integers for performance.
    // Because it can be sparse, it is represented as array of buckets by 256 chars each. Bucket can be null.
    // Values: >=  0 -> it is a normal char. Write the value (if <=256 then 1 byte, if <=65536 then 2 bytes, etc.).
    //         == UNASSIGNED -> no conversion found. Output a default char.
    //         <= SEQ_START  -> it's an index in encodeTableSeq, see below. The character starts a sequence.
    this.encodeTable = [];
    
    // `encodeTableSeq` is used when a sequence of unicode characters is encoded as a single code. We use a tree of
    // objects where keys correspond to characters in sequence and leafs are the encoded dbcs values. A special DEF_CHAR key
    // means end of sequence (needed when one sequence is a strict subsequence of another).
    // Objects are kept separately from encodeTable to increase performance.
    this.encodeTableSeq = [];

    // Some chars can be decoded, but need not be encoded.
    var skipEncodeChars = {};
    if (codecOptions.encodeSkipVals)
        for (var i = 0; i < codecOptions.encodeSkipVals.length; i++) {
            var val = codecOptions.encodeSkipVals[i];
            if (typeof val === 'number')
                skipEncodeChars[val] = true;
            else
                for (var j = val.from; j <= val.to; j++)
                    skipEncodeChars[j] = true;
        }
        
    // Use decode trie to recursively fill out encode tables.
    this._fillEncodeTable(0, 0, skipEncodeChars);

    // Add more encoding pairs when needed.
    if (codecOptions.encodeAdd) {
        for (var uChar in codecOptions.encodeAdd)
            if (Object.prototype.hasOwnProperty.call(codecOptions.encodeAdd, uChar))
                this._setEncodeChar(uChar.charCodeAt(0), codecOptions.encodeAdd[uChar]);
    }

    this.defCharSB  = this.encodeTable[0][iconv.defaultCharSingleByte.charCodeAt(0)];
    if (this.defCharSB === UNASSIGNED) this.defCharSB = this.encodeTable[0]['?'];
    if (this.defCharSB === UNASSIGNED) this.defCharSB = "?".charCodeAt(0);
}

DBCSCodec.prototype.encoder = DBCSEncoder;
DBCSCodec.prototype.decoder = DBCSDecoder;

// Decoder helpers
DBCSCodec.prototype._getDecodeTrieNode = function(addr) {
    var bytes = [];
    for (; addr > 0; addr >>>= 8)
        bytes.push(addr & 0xFF);
    if (bytes.length == 0)
        bytes.push(0);

    var node = this.decodeTables[0];
    for (var i = bytes.length-1; i > 0; i--) { // Traverse nodes deeper into the trie.
        var val = node[bytes[i]];

        if (val == UNASSIGNED) { // Create new node.
            node[bytes[i]] = NODE_START - this.decodeTables.length;
            this.decodeTables.push(node = UNASSIGNED_NODE.slice(0));
        }
        else if (val <= NODE_START) { // Existing node.
            node = this.decodeTables[NODE_START - val];
        }
        else
            throw new Error("Overwrite byte in " + this.encodingName + ", addr: " + addr.toString(16));
    }
    return node;
}


DBCSCodec.prototype._addDecodeChunk = function(chunk) {
    // First element of chunk is the hex mbcs code where we start.
    var curAddr = parseInt(chunk[0], 16);

    // Choose the decoding node where we'll write our chars.
    var writeTable = this._getDecodeTrieNode(curAddr);
    curAddr = curAddr & 0xFF;

    // Write all other elements of the chunk to the table.
    for (var k = 1; k < chunk.length; k++) {
        var part = chunk[k];
        if (typeof part === "string") { // String, write as-is.
            for (var l = 0; l < part.length;) {
                var code = part.charCodeAt(l++);
                if (0xD800 <= code && code < 0xDC00) { // Decode surrogate
                    var codeTrail = part.charCodeAt(l++);
                    if (0xDC00 <= codeTrail && codeTrail < 0xE000)
                        writeTable[curAddr++] = 0x10000 + (code - 0xD800) * 0x400 + (codeTrail - 0xDC00);
                    else
                        throw new Error("Incorrect surrogate pair in "  + this.encodingName + " at chunk " + chunk[0]);
                }
                else if (0x0FF0 < code && code <= 0x0FFF) { // Character sequence (our own encoding used)
                    var len = 0xFFF - code + 2;
                    var seq = [];
                    for (var m = 0; m < len; m++)
                        seq.push(part.charCodeAt(l++)); // Simple variation: don't support surrogates or subsequences in seq.

                    writeTable[curAddr++] = SEQ_START - this.decodeTableSeq.length;
                    this.decodeTableSeq.push(seq);
                }
                else
                    writeTable[curAddr++] = code; // Basic char
            }
        } 
        else if (typeof part === "number") { // Integer, meaning increasing sequence starting with prev character.
            var charCode = writeTable[curAddr - 1] + 1;
            for (var l = 0; l < part; l++)
                writeTable[curAddr++] = charCode++;
        }
        else
            throw new Error("Incorrect type '" + typeof part + "' given in "  + this.encodingName + " at chunk " + chunk[0]);
    }
    if (curAddr > 0xFF)
        throw new Error("Incorrect chunk in "  + this.encodingName + " at addr " + chunk[0] + ": too long" + curAddr);
}

// Encoder helpers
DBCSCodec.prototype._getEncodeBucket = function(uCode) {
    var high = uCode >> 8; // This could be > 0xFF because of astral characters.
    if (this.encodeTable[high] === undefined)
        this.encodeTable[high] = UNASSIGNED_NODE.slice(0); // Create bucket on demand.
    return this.encodeTable[high];
}

DBCSCodec.prototype._setEncodeChar = function(uCode, dbcsCode) {
    var bucket = this._getEncodeBucket(uCode);
    var low = uCode & 0xFF;
    if (bucket[low] <= SEQ_START)
        this.encodeTableSeq[SEQ_START-bucket[low]][DEF_CHAR] = dbcsCode; // There's already a sequence, set a single-char subsequence of it.
    else if (bucket[low] == UNASSIGNED)
        bucket[low] = dbcsCode;
}

DBCSCodec.prototype._setEncodeSequence = function(seq, dbcsCode) {
    
    // Get the root of character tree according to first character of the sequence.
    var uCode = seq[0];
    var bucket = this._getEncodeBucket(uCode);
    var low = uCode & 0xFF;

    var node;
    if (bucket[low] <= SEQ_START) {
        // There's already a sequence with  - use it.
        node = this.encodeTableSeq[SEQ_START-bucket[low]];
    }
    else {
        // There was no sequence object - allocate a new one.
        node = {};
        if (bucket[low] !== UNASSIGNED) node[DEF_CHAR] = bucket[low]; // If a char was set before - make it a single-char subsequence.
        bucket[low] = SEQ_START - this.encodeTableSeq.length;
        this.encodeTableSeq.push(node);
    }

    // Traverse the character tree, allocating new nodes as needed.
    for (var j = 1; j < seq.length-1; j++) {
        var oldVal = node[uCode];
        if (typeof oldVal === 'object')
            node = oldVal;
        else {
            node = node[uCode] = {}
            if (oldVal !== undefined)
                node[DEF_CHAR] = oldVal
        }
    }

    // Set the leaf to given dbcsCode.
    uCode = seq[seq.length-1];
    node[uCode] = dbcsCode;
}

DBCSCodec.prototype._fillEncodeTable = function(nodeIdx, prefix, skipEncodeChars) {
    var node = this.decodeTables[nodeIdx];
    var hasValues = false;
    var subNodeEmpty = {};
    for (var i = 0; i < 0x100; i++) {
        var uCode = node[i];
        var mbCode = prefix + i;
        if (skipEncodeChars[mbCode])
            continue;

        if (uCode >= 0) {
            this._setEncodeChar(uCode, mbCode);
            hasValues = true;
        } else if (uCode <= NODE_START) {
            var subNodeIdx = NODE_START - uCode;
            if (!subNodeEmpty[subNodeIdx]) {  // Skip empty subtrees (they are too large in gb18030).
                var newPrefix = (mbCode << 8) >>> 0;  // NOTE: '>>> 0' keeps 32-bit num positive.
                if (this._fillEncodeTable(subNodeIdx, newPrefix, skipEncodeChars))
                    hasValues = true;
                else
                    subNodeEmpty[subNodeIdx] = true;
            }
        } else if (uCode <= SEQ_START) {
            this._setEncodeSequence(this.decodeTableSeq[SEQ_START - uCode], mbCode);
            hasValues = true;
        }
    }
    return hasValues;
}



// == Encoder ==================================================================

function DBCSEncoder(options, codec) {
    // Encoder state
    this.leadSurrogate = -1;
    this.seqObj = undefined;
    
    // Static data
    this.encodeTable = codec.encodeTable;
    this.encodeTableSeq = codec.encodeTableSeq;
    this.defaultCharSingleByte = codec.defCharSB;
    this.gb18030 = codec.gb18030;
}

DBCSEncoder.prototype.write = function(str) {
    var newBuf = Buffer.alloc(str.length * (this.gb18030 ? 4 : 3)),
        leadSurrogate = this.leadSurrogate,
        seqObj = this.seqObj, nextChar = -1,
        i = 0, j = 0;

    while (true) {
        // 0. Get next character.
        if (nextChar === -1) {
            if (i == str.length) break;
            var uCode = str.charCodeAt(i++);
        }
        else {
            var uCode = nextChar;
            nextChar = -1;    
        }

        // 1. Handle surrogates.
        if (0xD800 <= uCode && uCode < 0xE000) { // Char is one of surrogates.
            if (uCode < 0xDC00) { // We've got lead surrogate.
                if (leadSurrogate === -1) {
                    leadSurrogate = uCode;
                    continue;
                } else {
                    leadSurrogate = uCode;
                    // Double lead surrogate found.
                    uCode = UNASSIGNED;
                }
            } else { // We've got trail surrogate.
                if (leadSurrogate !== -1) {
                    uCode = 0x10000 + (leadSurrogate - 0xD800) * 0x400 + (uCode - 0xDC00);
                    leadSurrogate = -1;
                } else {
                    // Incomplete surrogate pair - only trail surrogate found.
                    uCode = UNASSIGNED;
                }
                
            }
        }
        else if (leadSurrogate !== -1) {
            // Incomplete surrogate pair - only lead surrogate found.
            nextChar = uCode; uCode = UNASSIGNED; // Write an error, then current char.
            leadSurrogate = -1;
        }

        // 2. Convert uCode character.
        var dbcsCode = UNASSIGNED;
        if (seqObj !== undefined && uCode != UNASSIGNED) { // We are in the middle of the sequence
            var resCode = seqObj[uCode];
            if (typeof resCode === 'object') { // Sequence continues.
                seqObj = resCode;
                continue;

            } else if (typeof resCode == 'number') { // Sequence finished. Write it.
                dbcsCode = resCode;

            } else if (resCode == undefined) { // Current character is not part of the sequence.

                // Try default character for this sequence
                resCode = seqObj[DEF_CHAR];
                if (resCode !== undefined) {
                    dbcsCode = resCode; // Found. Write it.
                    nextChar = uCode; // Current character will be written too in the next iteration.

                } else {
                    // TODO: What if we have no default? (resCode == undefined)
                    // Then, we should write first char of the sequence as-is and try the rest recursively.
                    // Didn't do it for now because no encoding has this situation yet.
                    // Currently, just skip the sequence and write current char.
                }
            }
            seqObj = undefined;
        }
        else if (uCode >= 0) {  // Regular character
            var subtable = this.encodeTable[uCode >> 8];
            if (subtable !== undefined)
                dbcsCode = subtable[uCode & 0xFF];
            
            if (dbcsCode <= SEQ_START) { // Sequence start
                seqObj = this.encodeTableSeq[SEQ_START-dbcsCode];
                continue;
            }

            if (dbcsCode == UNASSIGNED && this.gb18030) {
                // Use GB18030 algorithm to find character(s) to write.
                var idx = findIdx(this.gb18030.uChars, uCode);
                if (idx != -1) {
                    var dbcsCode = this.gb18030.gbChars[idx] + (uCode - this.gb18030.uChars[idx]);
                    newBuf[j++] = 0x81 + Math.floor(dbcsCode / 12600); dbcsCode = dbcsCode % 12600;
                    newBuf[j++] = 0x30 + Math.floor(dbcsCode / 1260); dbcsCode = dbcsCode % 1260;
                    newBuf[j++] = 0x81 + Math.floor(dbcsCode / 10); dbcsCode = dbcsCode % 10;
                    newBuf[j++] = 0x30 + dbcsCode;
                    continue;
                }
            }
        }

        // 3. Write dbcsCode character.
        if (dbcsCode === UNASSIGNED)
            dbcsCode = this.defaultCharSingleByte;
        
        if (dbcsCode < 0x100) {
            newBuf[j++] = dbcsCode;
        }
        else if (dbcsCode < 0x10000) {
            newBuf[j++] = dbcsCode >> 8;   // high byte
            newBuf[j++] = dbcsCode & 0xFF; // low byte
        }
        else if (dbcsCode < 0x1000000) {
            newBuf[j++] = dbcsCode >> 16;
            newBuf[j++] = (dbcsCode >> 8) & 0xFF;
            newBuf[j++] = dbcsCode & 0xFF;
        } else {
            newBuf[j++] = dbcsCode >>> 24;
            newBuf[j++] = (dbcsCode >>> 16) & 0xFF;
            newBuf[j++] = (dbcsCode >>> 8) & 0xFF;
            newBuf[j++] = dbcsCode & 0xFF;
        }
    }

    this.seqObj = seqObj;
    this.leadSurrogate = leadSurrogate;
    return newBuf.slice(0, j);
}

DBCSEncoder.prototype.end = function() {
    if (this.leadSurrogate === -1 && this.seqObj === undefined)
        return; // All clean. Most often case.

    var newBuf = Buffer.alloc(10), j = 0;

    if (this.seqObj) { // We're in the sequence.
        var dbcsCode = this.seqObj[DEF_CHAR];
        if (dbcsCode !== undefined) { // Write beginning of the sequence.
            if (dbcsCode < 0x100) {
                newBuf[j++] = dbcsCode;
            }
            else {
                newBuf[j++] = dbcsCode >> 8;   // high byte
                newBuf[j++] = dbcsCode & 0xFF; // low byte
            }
        } else {
            // See todo above.
        }
        this.seqObj = undefined;
    }

    if (this.leadSurrogate !== -1) {
        // Incomplete surrogate pair - only lead surrogate found.
        newBuf[j++] = this.defaultCharSingleByte;
        this.leadSurrogate = -1;
    }
    
    return newBuf.slice(0, j);
}

// Export for testing
DBCSEncoder.prototype.findIdx = findIdx;


// == Decoder ==================================================================

function DBCSDecoder(options, codec) {
    // Decoder state
    this.nodeIdx = 0;
    this.prevBytes = [];

    // Static data
    this.decodeTables = codec.decodeTables;
    this.decodeTableSeq = codec.decodeTableSeq;
    this.defaultCharUnicode = codec.defaultCharUnicode;
    this.gb18030 = codec.gb18030;
}

DBCSDecoder.prototype.write = function(buf) {
    var newBuf = Buffer.alloc(buf.length*2),
        nodeIdx = this.nodeIdx, 
        prevBytes = this.prevBytes, prevOffset = this.prevBytes.length,
        seqStart = -this.prevBytes.length, // idx of the start of current parsed sequence.
        uCode;

    for (var i = 0, j = 0; i < buf.length; i++) {
        var curByte = (i >= 0) ? buf[i] : prevBytes[i + prevOffset];

        // Lookup in current trie node.
        var uCode = this.decodeTables[nodeIdx][curByte];

        if (uCode >= 0) { 
            // Normal character, just use it.
        }
        else if (uCode === UNASSIGNED) { // Unknown char.
            // TODO: Callback with seq.
            uCode = this.defaultCharUnicode.charCodeAt(0);
            i = seqStart; // Skip one byte ('i' will be incremented by the for loop) and try to parse again.
        }
        else if (uCode === GB18030_CODE) {
            if (i >= 3) {
                var ptr = (buf[i-3]-0x81)*12600 + (buf[i-2]-0x30)*1260 + (buf[i-1]-0x81)*10 + (curByte-0x30);
            } else {
                var ptr = (prevBytes[i-3+prevOffset]-0x81)*12600 + 
                          (((i-2 >= 0) ? buf[i-2] : prevBytes[i-2+prevOffset])-0x30)*1260 + 
                          (((i-1 >= 0) ? buf[i-1] : prevBytes[i-1+prevOffset])-0x81)*10 + 
                          (curByte-0x30);
            }
            var idx = findIdx(this.gb18030.gbChars, ptr);
            uCode = this.gb18030.uChars[idx] + ptr - this.gb18030.gbChars[idx];
        }
        else if (uCode <= NODE_START) { // Go to next trie node.
            nodeIdx = NODE_START - uCode;
            continue;
        }
        else if (uCode <= SEQ_START) { // Output a sequence of chars.
            var seq = this.decodeTableSeq[SEQ_START - uCode];
            for (var k = 0; k < seq.length - 1; k++) {
                uCode = seq[k];
                newBuf[j++] = uCode & 0xFF;
                newBuf[j++] = uCode >> 8;
            }
            uCode = seq[seq.length-1];
        }
        else
            throw new Error("iconv-lite internal error: invalid decoding table value " + uCode + " at " + nodeIdx + "/" + curByte);

        // Write the character to buffer, handling higher planes using surrogate pair.
        if (uCode >= 0x10000) { 
            uCode -= 0x10000;
            var uCodeLead = 0xD800 | (uCode >> 10);
            newBuf[j++] = uCodeLead & 0xFF;
            newBuf[j++] = uCodeLead >> 8;

            uCode = 0xDC00 | (uCode & 0x3FF);
        }
        newBuf[j++] = uCode & 0xFF;
        newBuf[j++] = uCode >> 8;

        // Reset trie node.
        nodeIdx = 0; seqStart = i+1;
    }

    this.nodeIdx = nodeIdx;
    this.prevBytes = (seqStart >= 0)
        ? Array.prototype.slice.call(buf, seqStart)
        : prevBytes.slice(seqStart + prevOffset).concat(Array.prototype.slice.call(buf));

    return newBuf.slice(0, j).toString('ucs2');
}

DBCSDecoder.prototype.end = function() {
    var ret = '';

    // Try to parse all remaining chars.
    while (this.prevBytes.length > 0) {
        // Skip 1 character in the buffer.
        ret += this.defaultCharUnicode;
        var bytesArr = this.prevBytes.slice(1);

        // Parse remaining as usual.
        this.prevBytes = [];
        this.nodeIdx = 0;
        if (bytesArr.length > 0)
            ret += this.write(bytesArr);
    }

    this.prevBytes = [];
    this.nodeIdx = 0;
    return ret;
}

// Binary search for GB18030. Returns largest i such that table[i] <= val.
function findIdx(table, val) {
    if (table[0] > val)
        return -1;

    var l = 0, r = table.length;
    while (l < r-1) { // always table[l] <= val < table[r]
        var mid = l + ((r-l+1) >> 1);
        if (table[mid] <= val)
            l = mid;
        else
            r = mid;
    }
    return l;
}



/***/ }),

/***/ 90481:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


// Description of supported double byte encodings and aliases.
// Tables are not require()-d until they are needed to speed up library load.
// require()-s are direct to support Browserify.

module.exports = {
    
    // == Japanese/ShiftJIS ====================================================
    // All japanese encodings are based on JIS X set of standards:
    // JIS X 0201 - Single-byte encoding of ASCII + ¥ + Kana chars at 0xA1-0xDF.
    // JIS X 0208 - Main set of 6879 characters, placed in 94x94 plane, to be encoded by 2 bytes. 
    //              Has several variations in 1978, 1983, 1990 and 1997.
    // JIS X 0212 - Supplementary plane of 6067 chars in 94x94 plane. 1990. Effectively dead.
    // JIS X 0213 - Extension and modern replacement of 0208 and 0212. Total chars: 11233.
    //              2 planes, first is superset of 0208, second - revised 0212.
    //              Introduced in 2000, revised 2004. Some characters are in Unicode Plane 2 (0x2xxxx)

    // Byte encodings are:
    //  * Shift_JIS: Compatible with 0201, uses not defined chars in top half as lead bytes for double-byte
    //               encoding of 0208. Lead byte ranges: 0x81-0x9F, 0xE0-0xEF; Trail byte ranges: 0x40-0x7E, 0x80-0x9E, 0x9F-0xFC.
    //               Windows CP932 is a superset of Shift_JIS. Some companies added more chars, notably KDDI.
    //  * EUC-JP:    Up to 3 bytes per character. Used mostly on *nixes.
    //               0x00-0x7F       - lower part of 0201
    //               0x8E, 0xA1-0xDF - upper part of 0201
    //               (0xA1-0xFE)x2   - 0208 plane (94x94).
    //               0x8F, (0xA1-0xFE)x2 - 0212 plane (94x94).
    //  * JIS X 208: 7-bit, direct encoding of 0208. Byte ranges: 0x21-0x7E (94 values). Uncommon.
    //               Used as-is in ISO2022 family.
    //  * ISO2022-JP: Stateful encoding, with escape sequences to switch between ASCII, 
    //                0201-1976 Roman, 0208-1978, 0208-1983.
    //  * ISO2022-JP-1: Adds esc seq for 0212-1990.
    //  * ISO2022-JP-2: Adds esc seq for GB2313-1980, KSX1001-1992, ISO8859-1, ISO8859-7.
    //  * ISO2022-JP-3: Adds esc seq for 0201-1976 Kana set, 0213-2000 Planes 1, 2.
    //  * ISO2022-JP-2004: Adds 0213-2004 Plane 1.
    //
    // After JIS X 0213 appeared, Shift_JIS-2004, EUC-JISX0213 and ISO2022-JP-2004 followed, with just changing the planes.
    //
    // Overall, it seems that it's a mess :( http://www8.plala.or.jp/tkubota1/unicode-symbols-map2.html

    'shiftjis': {
        type: '_dbcs',
        table: function() { return __webpack_require__(40679) },
        encodeAdd: {'\u00a5': 0x5C, '\u203E': 0x7E},
        encodeSkipVals: [{from: 0xED40, to: 0xF940}],
    },
    'csshiftjis': 'shiftjis',
    'mskanji': 'shiftjis',
    'sjis': 'shiftjis',
    'windows31j': 'shiftjis',
    'ms31j': 'shiftjis',
    'xsjis': 'shiftjis',
    'windows932': 'shiftjis',
    'ms932': 'shiftjis',
    '932': 'shiftjis',
    'cp932': 'shiftjis',

    'eucjp': {
        type: '_dbcs',
        table: function() { return __webpack_require__(56406) },
        encodeAdd: {'\u00a5': 0x5C, '\u203E': 0x7E},
    },

    // TODO: KDDI extension to Shift_JIS
    // TODO: IBM CCSID 942 = CP932, but F0-F9 custom chars and other char changes.
    // TODO: IBM CCSID 943 = Shift_JIS = CP932 with original Shift_JIS lower 128 chars.


    // == Chinese/GBK ==========================================================
    // http://en.wikipedia.org/wiki/GBK
    // We mostly implement W3C recommendation: https://www.w3.org/TR/encoding/#gbk-encoder

    // Oldest GB2312 (1981, ~7600 chars) is a subset of CP936
    'gb2312': 'cp936',
    'gb231280': 'cp936',
    'gb23121980': 'cp936',
    'csgb2312': 'cp936',
    'csiso58gb231280': 'cp936',
    'euccn': 'cp936',

    // Microsoft's CP936 is a subset and approximation of GBK.
    'windows936': 'cp936',
    'ms936': 'cp936',
    '936': 'cp936',
    'cp936': {
        type: '_dbcs',
        table: function() { return __webpack_require__(74488) },
    },

    // GBK (~22000 chars) is an extension of CP936 that added user-mapped chars and some other.
    'gbk': {
        type: '_dbcs',
        table: function() { return (__webpack_require__(74488).concat)(__webpack_require__(55914)) },
    },
    'xgbk': 'gbk',
    'isoir58': 'gbk',

    // GB18030 is an algorithmic extension of GBK.
    // Main source: https://www.w3.org/TR/encoding/#gbk-encoder
    // http://icu-project.org/docs/papers/gb18030.html
    // http://source.icu-project.org/repos/icu/data/trunk/charset/data/xml/gb-18030-2000.xml
    // http://www.khngai.com/chinese/charmap/tblgbk.php?page=0
    'gb18030': {
        type: '_dbcs',
        table: function() { return (__webpack_require__(74488).concat)(__webpack_require__(55914)) },
        gb18030: function() { return __webpack_require__(99129) },
        encodeSkipVals: [0x80],
        encodeAdd: {'€': 0xA2E3},
    },

    'chinese': 'gb18030',


    // == Korean ===============================================================
    // EUC-KR, KS_C_5601 and KS X 1001 are exactly the same.
    'windows949': 'cp949',
    'ms949': 'cp949',
    '949': 'cp949',
    'cp949': {
        type: '_dbcs',
        table: function() { return __webpack_require__(21166) },
    },

    'cseuckr': 'cp949',
    'csksc56011987': 'cp949',
    'euckr': 'cp949',
    'isoir149': 'cp949',
    'korean': 'cp949',
    'ksc56011987': 'cp949',
    'ksc56011989': 'cp949',
    'ksc5601': 'cp949',


    // == Big5/Taiwan/Hong Kong ================================================
    // There are lots of tables for Big5 and cp950. Please see the following links for history:
    // http://moztw.org/docs/big5/  http://www.haible.de/bruno/charsets/conversion-tables/Big5.html
    // Variations, in roughly number of defined chars:
    //  * Windows CP 950: Microsoft variant of Big5. Canonical: http://www.unicode.org/Public/MAPPINGS/VENDORS/MICSFT/WINDOWS/CP950.TXT
    //  * Windows CP 951: Microsoft variant of Big5-HKSCS-2001. Seems to be never public. http://me.abelcheung.org/articles/research/what-is-cp951/
    //  * Big5-2003 (Taiwan standard) almost superset of cp950.
    //  * Unicode-at-on (UAO) / Mozilla 1.8. Falling out of use on the Web. Not supported by other browsers.
    //  * Big5-HKSCS (-2001, -2004, -2008). Hong Kong standard. 
    //    many unicode code points moved from PUA to Supplementary plane (U+2XXXX) over the years.
    //    Plus, it has 4 combining sequences.
    //    Seems that Mozilla refused to support it for 10 yrs. https://bugzilla.mozilla.org/show_bug.cgi?id=162431 https://bugzilla.mozilla.org/show_bug.cgi?id=310299
    //    because big5-hkscs is the only encoding to include astral characters in non-algorithmic way.
    //    Implementations are not consistent within browsers; sometimes labeled as just big5.
    //    MS Internet Explorer switches from big5 to big5-hkscs when a patch applied.
    //    Great discussion & recap of what's going on https://bugzilla.mozilla.org/show_bug.cgi?id=912470#c31
    //    In the encoder, it might make sense to support encoding old PUA mappings to Big5 bytes seq-s.
    //    Official spec: http://www.ogcio.gov.hk/en/business/tech_promotion/ccli/terms/doc/2003cmp_2008.txt
    //                   http://www.ogcio.gov.hk/tc/business/tech_promotion/ccli/terms/doc/hkscs-2008-big5-iso.txt
    // 
    // Current understanding of how to deal with Big5(-HKSCS) is in the Encoding Standard, http://encoding.spec.whatwg.org/#big5-encoder
    // Unicode mapping (http://www.unicode.org/Public/MAPPINGS/OBSOLETE/EASTASIA/OTHER/BIG5.TXT) is said to be wrong.

    'windows950': 'cp950',
    'ms950': 'cp950',
    '950': 'cp950',
    'cp950': {
        type: '_dbcs',
        table: function() { return __webpack_require__(72324) },
    },

    // Big5 has many variations and is an extension of cp950. We use Encoding Standard's as a consensus.
    'big5': 'big5hkscs',
    'big5hkscs': {
        type: '_dbcs',
        table: function() { return (__webpack_require__(72324).concat)(__webpack_require__(43267)) },
        encodeSkipVals: [
            // Although Encoding Standard says we should avoid encoding to HKSCS area (See Step 1 of
            // https://encoding.spec.whatwg.org/#index-big5-pointer), we still do it to increase compatibility with ICU.
            // But if a single unicode point can be encoded both as HKSCS and regular Big5, we prefer the latter.
            0x8e69, 0x8e6f, 0x8e7e, 0x8eab, 0x8eb4, 0x8ecd, 0x8ed0, 0x8f57, 0x8f69, 0x8f6e, 0x8fcb, 0x8ffe,
            0x906d, 0x907a, 0x90c4, 0x90dc, 0x90f1, 0x91bf, 0x92af, 0x92b0, 0x92b1, 0x92b2, 0x92d1, 0x9447, 0x94ca,
            0x95d9, 0x96fc, 0x9975, 0x9b76, 0x9b78, 0x9b7b, 0x9bc6, 0x9bde, 0x9bec, 0x9bf6, 0x9c42, 0x9c53, 0x9c62,
            0x9c68, 0x9c6b, 0x9c77, 0x9cbc, 0x9cbd, 0x9cd0, 0x9d57, 0x9d5a, 0x9dc4, 0x9def, 0x9dfb, 0x9ea9, 0x9eef,
            0x9efd, 0x9f60, 0x9fcb, 0xa077, 0xa0dc, 0xa0df, 0x8fcc, 0x92c8, 0x9644, 0x96ed,

            // Step 2 of https://encoding.spec.whatwg.org/#index-big5-pointer: Use last pointer for U+2550, U+255E, U+2561, U+256A, U+5341, or U+5345
            0xa2a4, 0xa2a5, 0xa2a7, 0xa2a6, 0xa2cc, 0xa2ce,
        ],
    },

    'cnbig5': 'big5hkscs',
    'csbig5': 'big5hkscs',
    'xxbig5': 'big5hkscs',
};


/***/ }),

/***/ 26326:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


// Update this array if you add/rename/remove files in this directory.
// We support Browserify by skipping automatic module discovery and requiring modules directly.
var modules = [
    __webpack_require__(16793),
    __webpack_require__(24162),
    __webpack_require__(17100),
    __webpack_require__(11326),
    __webpack_require__(99948),
    __webpack_require__(99900),
    __webpack_require__(81492),
    __webpack_require__(35143),
    __webpack_require__(90481),
];

// Put all encoding/alias/codec definitions to single object and export it.
for (var i = 0; i < modules.length; i++) {
    var module = modules[i];
    for (var enc in module)
        if (Object.prototype.hasOwnProperty.call(module, enc))
            exports[enc] = module[enc];
}


/***/ }),

/***/ 16793:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";

var Buffer = (__webpack_require__(93143).Buffer);

// Export Node.js internal encodings.

module.exports = {
    // Encodings
    utf8:   { type: "_internal", bomAware: true},
    cesu8:  { type: "_internal", bomAware: true},
    unicode11utf8: "utf8",

    ucs2:   { type: "_internal", bomAware: true},
    utf16le: "ucs2",

    binary: { type: "_internal" },
    base64: { type: "_internal" },
    hex:    { type: "_internal" },

    // Codec.
    _internal: InternalCodec,
};

//------------------------------------------------------------------------------

function InternalCodec(codecOptions, iconv) {
    this.enc = codecOptions.encodingName;
    this.bomAware = codecOptions.bomAware;

    if (this.enc === "base64")
        this.encoder = InternalEncoderBase64;
    else if (this.enc === "cesu8") {
        this.enc = "utf8"; // Use utf8 for decoding.
        this.encoder = InternalEncoderCesu8;

        // Add decoder for versions of Node not supporting CESU-8
        if (Buffer.from('eda0bdedb2a9', 'hex').toString() !== '💩') {
            this.decoder = InternalDecoderCesu8;
            this.defaultCharUnicode = iconv.defaultCharUnicode;
        }
    }
}

InternalCodec.prototype.encoder = InternalEncoder;
InternalCodec.prototype.decoder = InternalDecoder;

//------------------------------------------------------------------------------

// We use node.js internal decoder. Its signature is the same as ours.
var StringDecoder = (__webpack_require__(43143)/* .StringDecoder */ .I);

if (!StringDecoder.prototype.end) // Node v0.8 doesn't have this method.
    StringDecoder.prototype.end = function() {};


function InternalDecoder(options, codec) {
    this.decoder = new StringDecoder(codec.enc);
}

InternalDecoder.prototype.write = function(buf) {
    if (!Buffer.isBuffer(buf)) {
        buf = Buffer.from(buf);
    }

    return this.decoder.write(buf);
}

InternalDecoder.prototype.end = function() {
    return this.decoder.end();
}


//------------------------------------------------------------------------------
// Encoder is mostly trivial

function InternalEncoder(options, codec) {
    this.enc = codec.enc;
}

InternalEncoder.prototype.write = function(str) {
    return Buffer.from(str, this.enc);
}

InternalEncoder.prototype.end = function() {
}


//------------------------------------------------------------------------------
// Except base64 encoder, which must keep its state.

function InternalEncoderBase64(options, codec) {
    this.prevStr = '';
}

InternalEncoderBase64.prototype.write = function(str) {
    str = this.prevStr + str;
    var completeQuads = str.length - (str.length % 4);
    this.prevStr = str.slice(completeQuads);
    str = str.slice(0, completeQuads);

    return Buffer.from(str, "base64");
}

InternalEncoderBase64.prototype.end = function() {
    return Buffer.from(this.prevStr, "base64");
}


//------------------------------------------------------------------------------
// CESU-8 encoder is also special.

function InternalEncoderCesu8(options, codec) {
}

InternalEncoderCesu8.prototype.write = function(str) {
    var buf = Buffer.alloc(str.length * 3), bufIdx = 0;
    for (var i = 0; i < str.length; i++) {
        var charCode = str.charCodeAt(i);
        // Naive implementation, but it works because CESU-8 is especially easy
        // to convert from UTF-16 (which all JS strings are encoded in).
        if (charCode < 0x80)
            buf[bufIdx++] = charCode;
        else if (charCode < 0x800) {
            buf[bufIdx++] = 0xC0 + (charCode >>> 6);
            buf[bufIdx++] = 0x80 + (charCode & 0x3f);
        }
        else { // charCode will always be < 0x10000 in javascript.
            buf[bufIdx++] = 0xE0 + (charCode >>> 12);
            buf[bufIdx++] = 0x80 + ((charCode >>> 6) & 0x3f);
            buf[bufIdx++] = 0x80 + (charCode & 0x3f);
        }
    }
    return buf.slice(0, bufIdx);
}

InternalEncoderCesu8.prototype.end = function() {
}

//------------------------------------------------------------------------------
// CESU-8 decoder is not implemented in Node v4.0+

function InternalDecoderCesu8(options, codec) {
    this.acc = 0;
    this.contBytes = 0;
    this.accBytes = 0;
    this.defaultCharUnicode = codec.defaultCharUnicode;
}

InternalDecoderCesu8.prototype.write = function(buf) {
    var acc = this.acc, contBytes = this.contBytes, accBytes = this.accBytes, 
        res = '';
    for (var i = 0; i < buf.length; i++) {
        var curByte = buf[i];
        if ((curByte & 0xC0) !== 0x80) { // Leading byte
            if (contBytes > 0) { // Previous code is invalid
                res += this.defaultCharUnicode;
                contBytes = 0;
            }

            if (curByte < 0x80) { // Single-byte code
                res += String.fromCharCode(curByte);
            } else if (curByte < 0xE0) { // Two-byte code
                acc = curByte & 0x1F;
                contBytes = 1; accBytes = 1;
            } else if (curByte < 0xF0) { // Three-byte code
                acc = curByte & 0x0F;
                contBytes = 2; accBytes = 1;
            } else { // Four or more are not supported for CESU-8.
                res += this.defaultCharUnicode;
            }
        } else { // Continuation byte
            if (contBytes > 0) { // We're waiting for it.
                acc = (acc << 6) | (curByte & 0x3f);
                contBytes--; accBytes++;
                if (contBytes === 0) {
                    // Check for overlong encoding, but support Modified UTF-8 (encoding NULL as C0 80)
                    if (accBytes === 2 && acc < 0x80 && acc > 0)
                        res += this.defaultCharUnicode;
                    else if (accBytes === 3 && acc < 0x800)
                        res += this.defaultCharUnicode;
                    else
                        // Actually add character.
                        res += String.fromCharCode(acc);
                }
            } else { // Unexpected continuation byte
                res += this.defaultCharUnicode;
            }
        }
    }
    this.acc = acc; this.contBytes = contBytes; this.accBytes = accBytes;
    return res;
}

InternalDecoderCesu8.prototype.end = function() {
    var res = 0;
    if (this.contBytes > 0)
        res += this.defaultCharUnicode;
    return res;
}


/***/ }),

/***/ 99948:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";

var Buffer = (__webpack_require__(93143).Buffer);

// Single-byte codec. Needs a 'chars' string parameter that contains 256 or 128 chars that
// correspond to encoded bytes (if 128 - then lower half is ASCII). 

exports._sbcs = SBCSCodec;
function SBCSCodec(codecOptions, iconv) {
    if (!codecOptions)
        throw new Error("SBCS codec is called without the data.")
    
    // Prepare char buffer for decoding.
    if (!codecOptions.chars || (codecOptions.chars.length !== 128 && codecOptions.chars.length !== 256))
        throw new Error("Encoding '"+codecOptions.type+"' has incorrect 'chars' (must be of len 128 or 256)");
    
    if (codecOptions.chars.length === 128) {
        var asciiString = "";
        for (var i = 0; i < 128; i++)
            asciiString += String.fromCharCode(i);
        codecOptions.chars = asciiString + codecOptions.chars;
    }

    this.decodeBuf = Buffer.from(codecOptions.chars, 'ucs2');
    
    // Encoding buffer.
    var encodeBuf = Buffer.alloc(65536, iconv.defaultCharSingleByte.charCodeAt(0));

    for (var i = 0; i < codecOptions.chars.length; i++)
        encodeBuf[codecOptions.chars.charCodeAt(i)] = i;

    this.encodeBuf = encodeBuf;
}

SBCSCodec.prototype.encoder = SBCSEncoder;
SBCSCodec.prototype.decoder = SBCSDecoder;


function SBCSEncoder(options, codec) {
    this.encodeBuf = codec.encodeBuf;
}

SBCSEncoder.prototype.write = function(str) {
    var buf = Buffer.alloc(str.length);
    for (var i = 0; i < str.length; i++)
        buf[i] = this.encodeBuf[str.charCodeAt(i)];
    
    return buf;
}

SBCSEncoder.prototype.end = function() {
}


function SBCSDecoder(options, codec) {
    this.decodeBuf = codec.decodeBuf;
}

SBCSDecoder.prototype.write = function(buf) {
    // Strings are immutable in JS -> we use ucs2 buffer to speed up computations.
    var decodeBuf = this.decodeBuf;
    var newBuf = Buffer.alloc(buf.length*2);
    var idx1 = 0, idx2 = 0;
    for (var i = 0; i < buf.length; i++) {
        idx1 = buf[i]*2; idx2 = i*2;
        newBuf[idx2] = decodeBuf[idx1];
        newBuf[idx2+1] = decodeBuf[idx1+1];
    }
    return newBuf.toString('ucs2');
}

SBCSDecoder.prototype.end = function() {
}


/***/ }),

/***/ 81492:
/***/ (function(module) {

"use strict";


// Generated data for sbcs codec. Don't edit manually. Regenerate using generation/gen-sbcs.js script.
module.exports = {
  "437": "cp437",
  "737": "cp737",
  "775": "cp775",
  "850": "cp850",
  "852": "cp852",
  "855": "cp855",
  "856": "cp856",
  "857": "cp857",
  "858": "cp858",
  "860": "cp860",
  "861": "cp861",
  "862": "cp862",
  "863": "cp863",
  "864": "cp864",
  "865": "cp865",
  "866": "cp866",
  "869": "cp869",
  "874": "windows874",
  "922": "cp922",
  "1046": "cp1046",
  "1124": "cp1124",
  "1125": "cp1125",
  "1129": "cp1129",
  "1133": "cp1133",
  "1161": "cp1161",
  "1162": "cp1162",
  "1163": "cp1163",
  "1250": "windows1250",
  "1251": "windows1251",
  "1252": "windows1252",
  "1253": "windows1253",
  "1254": "windows1254",
  "1255": "windows1255",
  "1256": "windows1256",
  "1257": "windows1257",
  "1258": "windows1258",
  "28591": "iso88591",
  "28592": "iso88592",
  "28593": "iso88593",
  "28594": "iso88594",
  "28595": "iso88595",
  "28596": "iso88596",
  "28597": "iso88597",
  "28598": "iso88598",
  "28599": "iso88599",
  "28600": "iso885910",
  "28601": "iso885911",
  "28603": "iso885913",
  "28604": "iso885914",
  "28605": "iso885915",
  "28606": "iso885916",
  "windows874": {
    "type": "_sbcs",
    "chars": "€����…�����������‘’“”•–—�������� กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "win874": "windows874",
  "cp874": "windows874",
  "windows1250": {
    "type": "_sbcs",
    "chars": "€�‚�„…†‡�‰Š‹ŚŤŽŹ�‘’“”•–—�™š›śťžź ˇ˘Ł¤Ą¦§¨©Ş«¬­®Ż°±˛ł´µ¶·¸ąş»Ľ˝ľżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢßŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙"
  },
  "win1250": "windows1250",
  "cp1250": "windows1250",
  "windows1251": {
    "type": "_sbcs",
    "chars": "ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏђ‘’“”•–—�™љ›њќћџ ЎўЈ¤Ґ¦§Ё©Є«¬­®Ї°±Ііґµ¶·ё№є»јЅѕїАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "win1251": "windows1251",
  "cp1251": "windows1251",
  "windows1252": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰Š‹Œ�Ž��‘’“”•–—˜™š›œ�žŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "win1252": "windows1252",
  "cp1252": "windows1252",
  "windows1253": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡�‰�‹�����‘’“”•–—�™�›���� ΅Ά£¤¥¦§¨©�«¬­®―°±²³΄µ¶·ΈΉΊ»Ό½ΎΏΐΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡ�ΣΤΥΦΧΨΩΪΫάέήίΰαβγδεζηθικλμνξοπρςστυφχψωϊϋόύώ�"
  },
  "win1253": "windows1253",
  "cp1253": "windows1253",
  "windows1254": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰Š‹Œ����‘’“”•–—˜™š›œ��Ÿ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏĞÑÒÓÔÕÖ×ØÙÚÛÜİŞßàáâãäåæçèéêëìíîïğñòóôõö÷øùúûüışÿ"
  },
  "win1254": "windows1254",
  "cp1254": "windows1254",
  "windows1255": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰�‹�����‘’“”•–—˜™�›���� ¡¢£₪¥¦§¨©×«¬­®¯°±²³´µ¶·¸¹÷»¼½¾¿ְֱֲֳִֵֶַָֹֺֻּֽ־ֿ׀ׁׂ׃װױײ׳״�������אבגדהוזחטיךכלםמןנסעףפץצקרשת��‎‏�"
  },
  "win1255": "windows1255",
  "cp1255": "windows1255",
  "windows1256": {
    "type": "_sbcs",
    "chars": "€پ‚ƒ„…†‡ˆ‰ٹ‹Œچژڈگ‘’“”•–—ک™ڑ›œ‌‍ں ،¢£¤¥¦§¨©ھ«¬­®¯°±²³´µ¶·¸¹؛»¼½¾؟ہءآأؤإئابةتثجحخدذرزسشصض×طظعغـفقكàلâمنهوçèéêëىيîïًٌٍَôُِ÷ّùْûü‎‏ے"
  },
  "win1256": "windows1256",
  "cp1256": "windows1256",
  "windows1257": {
    "type": "_sbcs",
    "chars": "€�‚�„…†‡�‰�‹�¨ˇ¸�‘’“”•–—�™�›�¯˛� �¢£¤�¦§Ø©Ŗ«¬­®Æ°±²³´µ¶·ø¹ŗ»¼½¾æĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖ×ŲŁŚŪÜŻŽßąįāćäåęēčéźėģķīļšńņóōõö÷ųłśūüżž˙"
  },
  "win1257": "windows1257",
  "cp1257": "windows1257",
  "windows1258": {
    "type": "_sbcs",
    "chars": "€�‚ƒ„…†‡ˆ‰�‹Œ����‘’“”•–—˜™�›œ��Ÿ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "win1258": "windows1258",
  "cp1258": "windows1258",
  "iso88591": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "cp28591": "iso88591",
  "iso88592": {
    "type": "_sbcs",
    "chars": " Ą˘Ł¤ĽŚ§¨ŠŞŤŹ­ŽŻ°ą˛ł´ľśˇ¸šşťź˝žżŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢßŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙"
  },
  "cp28592": "iso88592",
  "iso88593": {
    "type": "_sbcs",
    "chars": " Ħ˘£¤�Ĥ§¨İŞĞĴ­�Ż°ħ²³´µĥ·¸ışğĵ½�żÀÁÂ�ÄĊĈÇÈÉÊËÌÍÎÏ�ÑÒÓÔĠÖ×ĜÙÚÛÜŬŜßàáâ�äċĉçèéêëìíîï�ñòóôġö÷ĝùúûüŭŝ˙"
  },
  "cp28593": "iso88593",
  "iso88594": {
    "type": "_sbcs",
    "chars": " ĄĸŖ¤ĨĻ§¨ŠĒĢŦ­Ž¯°ą˛ŗ´ĩļˇ¸šēģŧŊžŋĀÁÂÃÄÅÆĮČÉĘËĖÍÎĪĐŅŌĶÔÕÖ×ØŲÚÛÜŨŪßāáâãäåæįčéęëėíîīđņōķôõö÷øųúûüũū˙"
  },
  "cp28594": "iso88594",
  "iso88595": {
    "type": "_sbcs",
    "chars": " ЁЂЃЄЅІЇЈЉЊЋЌ­ЎЏАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя№ёђѓєѕіїјљњћќ§ўџ"
  },
  "cp28595": "iso88595",
  "iso88596": {
    "type": "_sbcs",
    "chars": " ���¤�������،­�������������؛���؟�ءآأؤإئابةتثجحخدذرزسشصضطظعغ�����ـفقكلمنهوىيًٌٍَُِّْ�������������"
  },
  "cp28596": "iso88596",
  "iso88597": {
    "type": "_sbcs",
    "chars": " ‘’£€₯¦§¨©ͺ«¬­�―°±²³΄΅Ά·ΈΉΊ»Ό½ΎΏΐΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡ�ΣΤΥΦΧΨΩΪΫάέήίΰαβγδεζηθικλμνξοπρςστυφχψωϊϋόύώ�"
  },
  "cp28597": "iso88597",
  "iso88598": {
    "type": "_sbcs",
    "chars": " �¢£¤¥¦§¨©×«¬­®¯°±²³´µ¶·¸¹÷»¼½¾��������������������������������‗אבגדהוזחטיךכלםמןנסעףפץצקרשת��‎‏�"
  },
  "cp28598": "iso88598",
  "iso88599": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏĞÑÒÓÔÕÖ×ØÙÚÛÜİŞßàáâãäåæçèéêëìíîïğñòóôõö÷øùúûüışÿ"
  },
  "cp28599": "iso88599",
  "iso885910": {
    "type": "_sbcs",
    "chars": " ĄĒĢĪĨĶ§ĻĐŠŦŽ­ŪŊ°ąēģīĩķ·ļđšŧž―ūŋĀÁÂÃÄÅÆĮČÉĘËĖÍÎÏÐŅŌÓÔÕÖŨØŲÚÛÜÝÞßāáâãäåæįčéęëėíîïðņōóôõöũøųúûüýþĸ"
  },
  "cp28600": "iso885910",
  "iso885911": {
    "type": "_sbcs",
    "chars": " กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "cp28601": "iso885911",
  "iso885913": {
    "type": "_sbcs",
    "chars": " ”¢£¤„¦§Ø©Ŗ«¬­®Æ°±²³“µ¶·ø¹ŗ»¼½¾æĄĮĀĆÄÅĘĒČÉŹĖĢĶĪĻŠŃŅÓŌÕÖ×ŲŁŚŪÜŻŽßąįāćäåęēčéźėģķīļšńņóōõö÷ųłśūüżž’"
  },
  "cp28603": "iso885913",
  "iso885914": {
    "type": "_sbcs",
    "chars": " Ḃḃ£ĊċḊ§Ẁ©ẂḋỲ­®ŸḞḟĠġṀṁ¶ṖẁṗẃṠỳẄẅṡÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏŴÑÒÓÔÕÖṪØÙÚÛÜÝŶßàáâãäåæçèéêëìíîïŵñòóôõöṫøùúûüýŷÿ"
  },
  "cp28604": "iso885914",
  "iso885915": {
    "type": "_sbcs",
    "chars": " ¡¢£€¥Š§š©ª«¬­®¯°±²³Žµ¶·ž¹º»ŒœŸ¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖ×ØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "cp28605": "iso885915",
  "iso885916": {
    "type": "_sbcs",
    "chars": " ĄąŁ€„Š§š©Ș«Ź­źŻ°±ČłŽ”¶·žčș»ŒœŸżÀÁÂĂÄĆÆÇÈÉÊËÌÍÎÏĐŃÒÓÔŐÖŚŰÙÚÛÜĘȚßàáâăäćæçèéêëìíîïđńòóôőöśűùúûüęțÿ"
  },
  "cp28606": "iso885916",
  "cp437": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜ¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm437": "cp437",
  "csibm437": "cp437",
  "cp737": {
    "type": "_sbcs",
    "chars": "ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζηθικλμνξοπρσςτυφχψ░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀ωάέήϊίόύϋώΆΈΉΊΌΎΏ±≥≤ΪΫ÷≈°∙·√ⁿ²■ "
  },
  "ibm737": "cp737",
  "csibm737": "cp737",
  "cp775": {
    "type": "_sbcs",
    "chars": "ĆüéāäģåćłēŖŗīŹÄÅÉæÆōöĢ¢ŚśÖÜø£Ø×¤ĀĪóŻżź”¦©®¬½¼Ł«»░▒▓│┤ĄČĘĖ╣║╗╝ĮŠ┐└┴┬├─┼ŲŪ╚╔╩╦╠═╬Žąčęėįšųūž┘┌█▄▌▐▀ÓßŌŃõÕµńĶķĻļņĒŅ’­±“¾¶§÷„°∙·¹³²■ "
  },
  "ibm775": "cp775",
  "csibm775": "cp775",
  "cp850": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈıÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm850": "cp850",
  "csibm850": "cp850",
  "cp852": {
    "type": "_sbcs",
    "chars": "ÇüéâäůćçłëŐőîŹÄĆÉĹĺôöĽľŚśÖÜŤťŁ×čáíóúĄąŽžĘę¬źČş«»░▒▓│┤ÁÂĚŞ╣║╗╝Żż┐└┴┬├─┼Ăă╚╔╩╦╠═╬¤đĐĎËďŇÍÎě┘┌█▄ŢŮ▀ÓßÔŃńňŠšŔÚŕŰýÝţ´­˝˛ˇ˘§÷¸°¨˙űŘř■ "
  },
  "ibm852": "cp852",
  "csibm852": "cp852",
  "cp855": {
    "type": "_sbcs",
    "chars": "ђЂѓЃёЁєЄѕЅіІїЇјЈљЉњЊћЋќЌўЎџЏюЮъЪаАбБцЦдДеЕфФгГ«»░▒▓│┤хХиИ╣║╗╝йЙ┐└┴┬├─┼кК╚╔╩╦╠═╬¤лЛмМнНоОп┘┌█▄Пя▀ЯрРсСтТуУжЖвВьЬ№­ыЫзЗшШэЭщЩчЧ§■ "
  },
  "ibm855": "cp855",
  "csibm855": "cp855",
  "cp856": {
    "type": "_sbcs",
    "chars": "אבגדהוזחטיךכלםמןנסעףפץצקרשת�£�×����������®¬½¼�«»░▒▓│┤���©╣║╗╝¢¥┐└┴┬├─┼��╚╔╩╦╠═╬¤���������┘┌█▄¦�▀������µ�������¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm856": "cp856",
  "csibm856": "cp856",
  "cp857": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîıÄÅÉæÆôöòûùİÖÜø£ØŞşáíóúñÑĞğ¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ºªÊËÈ�ÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµ�×ÚÛÙìÿ¯´­±�¾¶§÷¸°¨·¹³²■ "
  },
  "ibm857": "cp857",
  "csibm857": "cp857",
  "cp858": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø×ƒáíóúñÑªº¿®¬½¼¡«»░▒▓│┤ÁÂÀ©╣║╗╝¢¥┐└┴┬├─┼ãÃ╚╔╩╦╠═╬¤ðÐÊËÈ€ÍÎÏ┘┌█▄¦Ì▀ÓßÔÒõÕµþÞÚÛÙýÝ¯´­±‗¾¶§÷¸°¨·¹³²■ "
  },
  "ibm858": "cp858",
  "csibm858": "cp858",
  "cp860": {
    "type": "_sbcs",
    "chars": "ÇüéâãàÁçêÊèÍÔìÃÂÉÀÈôõòÚùÌÕÜ¢£Ù₧ÓáíóúñÑªº¿Ò¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm860": "cp860",
  "csibm860": "cp860",
  "cp861": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèÐðÞÄÅÉæÆôöþûÝýÖÜø£Ø₧ƒáíóúÁÍÓÚ¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm861": "cp861",
  "csibm861": "cp861",
  "cp862": {
    "type": "_sbcs",
    "chars": "אבגדהוזחטיךכלםמןנסעףפץצקרשת¢£¥₧ƒáíóúñÑªº¿⌐¬½¼¡«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm862": "cp862",
  "csibm862": "cp862",
  "cp863": {
    "type": "_sbcs",
    "chars": "ÇüéâÂà¶çêëèïî‗À§ÉÈÊôËÏûù¤ÔÜ¢£ÙÛƒ¦´óú¨¸³¯Î⌐¬½¼¾«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm863": "cp863",
  "csibm863": "cp863",
  "cp864": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$٪&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~°·∙√▒─│┼┤┬├┴┐┌└┘β∞φ±½¼≈«»ﻷﻸ��ﻻﻼ� ­ﺂ£¤ﺄ��ﺎﺏﺕﺙ،ﺝﺡﺥ٠١٢٣٤٥٦٧٨٩ﻑ؛ﺱﺵﺹ؟¢ﺀﺁﺃﺅﻊﺋﺍﺑﺓﺗﺛﺟﺣﺧﺩﺫﺭﺯﺳﺷﺻﺿﻁﻅﻋﻏ¦¬÷×ﻉـﻓﻗﻛﻟﻣﻧﻫﻭﻯﻳﺽﻌﻎﻍﻡﹽّﻥﻩﻬﻰﻲﻐﻕﻵﻶﻝﻙﻱ■�"
  },
  "ibm864": "cp864",
  "csibm864": "cp864",
  "cp865": {
    "type": "_sbcs",
    "chars": "ÇüéâäàåçêëèïîìÄÅÉæÆôöòûùÿÖÜø£Ø₧ƒáíóúñÑªº¿⌐¬½¼¡«¤░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
  },
  "ibm865": "cp865",
  "csibm865": "cp865",
  "cp866": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёЄєЇїЎў°∙·√№¤■ "
  },
  "ibm866": "cp866",
  "csibm866": "cp866",
  "cp869": {
    "type": "_sbcs",
    "chars": "������Ά�·¬¦‘’Έ―ΉΊΪΌ��ΎΫ©Ώ²³ά£έήίϊΐόύΑΒΓΔΕΖΗ½ΘΙ«»░▒▓│┤ΚΛΜΝ╣║╗╝ΞΟ┐└┴┬├─┼ΠΡ╚╔╩╦╠═╬ΣΤΥΦΧΨΩαβγ┘┌█▄δε▀ζηθικλμνξοπρσςτ΄­±υφχ§ψ΅°¨ωϋΰώ■ "
  },
  "ibm869": "cp869",
  "csibm869": "cp869",
  "cp922": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§¨©ª«¬­®‾°±²³´µ¶·¸¹º»¼½¾¿ÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏŠÑÒÓÔÕÖ×ØÙÚÛÜÝŽßàáâãäåæçèéêëìíîïšñòóôõö÷øùúûüýžÿ"
  },
  "ibm922": "cp922",
  "csibm922": "cp922",
  "cp1046": {
    "type": "_sbcs",
    "chars": "ﺈ×÷ﹱ■│─┐┌└┘ﹹﹻﹽﹿﹷﺊﻰﻳﻲﻎﻏﻐﻶﻸﻺﻼ ¤ﺋﺑﺗﺛﺟﺣ،­ﺧﺳ٠١٢٣٤٥٦٧٨٩ﺷ؛ﺻﺿﻊ؟ﻋءآأؤإئابةتثجحخدذرزسشصضطﻇعغﻌﺂﺄﺎﻓـفقكلمنهوىيًٌٍَُِّْﻗﻛﻟﻵﻷﻹﻻﻣﻧﻬﻩ�"
  },
  "ibm1046": "cp1046",
  "csibm1046": "cp1046",
  "cp1124": {
    "type": "_sbcs",
    "chars": " ЁЂҐЄЅІЇЈЉЊЋЌ­ЎЏАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя№ёђґєѕіїјљњћќ§ўџ"
  },
  "ibm1124": "cp1124",
  "csibm1124": "cp1124",
  "cp1125": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёҐґЄєІіЇї·√№¤■ "
  },
  "ibm1125": "cp1125",
  "csibm1125": "cp1125",
  "cp1129": {
    "type": "_sbcs",
    "chars": " ¡¢£¤¥¦§œ©ª«¬­®¯°±²³Ÿµ¶·Œ¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "ibm1129": "cp1129",
  "csibm1129": "cp1129",
  "cp1133": {
    "type": "_sbcs",
    "chars": " ກຂຄງຈສຊຍດຕຖທນບປຜຝພຟມຢຣລວຫອຮ���ຯະາຳິີຶືຸູຼັົຽ���ເແໂໃໄ່້໊໋໌ໍໆ�ໜໝ₭����������������໐໑໒໓໔໕໖໗໘໙��¢¬¦�"
  },
  "ibm1133": "cp1133",
  "csibm1133": "cp1133",
  "cp1161": {
    "type": "_sbcs",
    "chars": "��������������������������������่กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู้๊๋€฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛¢¬¦ "
  },
  "ibm1161": "cp1161",
  "csibm1161": "cp1161",
  "cp1162": {
    "type": "_sbcs",
    "chars": "€…‘’“”•–— กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  },
  "ibm1162": "cp1162",
  "csibm1162": "cp1162",
  "cp1163": {
    "type": "_sbcs",
    "chars": " ¡¢£€¥¦§œ©ª«¬­®¯°±²³Ÿµ¶·Œ¹º»¼½¾¿ÀÁÂĂÄÅÆÇÈÉÊË̀ÍÎÏĐÑ̉ÓÔƠÖ×ØÙÚÛÜỮßàáâăäåæçèéêë́íîïđṇ̃óôơö÷øùúûüư₫ÿ"
  },
  "ibm1163": "cp1163",
  "csibm1163": "cp1163",
  "maccroatian": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®Š™´¨≠ŽØ∞±≤≥∆µ∂∑∏š∫ªºΩžø¿¡¬√ƒ≈Ć«Č… ÀÃÕŒœĐ—“”‘’÷◊�©⁄¤‹›Æ»–·‚„‰ÂćÁčÈÍÎÏÌÓÔđÒÚÛÙıˆ˜¯πË˚¸Êæˇ"
  },
  "maccyrillic": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ†°¢£§•¶І®©™Ђђ≠Ѓѓ∞±≤≥іµ∂ЈЄєЇїЉљЊњјЅ¬√ƒ≈∆«»… ЋћЌќѕ–—“”‘’÷„ЎўЏџ№Ёёяабвгдежзийклмнопрстуфхцчшщъыьэю¤"
  },
  "macgreek": {
    "type": "_sbcs",
    "chars": "Ä¹²É³ÖÜ΅àâä΄¨çéèêë£™îï•½‰ôö¦­ùûü†ΓΔΘΛΞΠß®©ΣΪ§≠°·Α±≤≥¥ΒΕΖΗΙΚΜΦΫΨΩάΝ¬ΟΡ≈Τ«»… ΥΧΆΈœ–―“”‘’÷ΉΊΌΎέήίόΏύαβψδεφγηιξκλμνοπώρστθωςχυζϊϋΐΰ�"
  },
  "maciceland": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûüÝ°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤ÐðÞþý·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macroman": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macromania": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ĂŞ∞±≤≥¥µ∂∑∏π∫ªºΩăş¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›Ţţ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macthai": {
    "type": "_sbcs",
    "chars": "«»…“”�•‘’� กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู﻿​–—฿เแโใไๅๆ็่้๊๋์ํ™๏๐๑๒๓๔๕๖๗๘๙®©����"
  },
  "macturkish": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸĞğİıŞş‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙ�ˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "macukraine": {
    "type": "_sbcs",
    "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ†°Ґ£§•¶І®©™Ђђ≠Ѓѓ∞±≤≥іµґЈЄєЇїЉљЊњјЅ¬√ƒ≈∆«»… ЋћЌќѕ–—“”‘’÷„ЎўЏџ№Ёёяабвгдежзийклмнопрстуфхцчшщъыьэю¤"
  },
  "koi8r": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ё╓╔╕╖╗╘╙╚╛╜╝╞╟╠╡Ё╢╣╤╥╦╧╨╩╪╫╬©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8u": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ёє╔ії╗╘╙╚╛ґ╝╞╟╠╡ЁЄ╣ІЇ╦╧╨╩╪Ґ╬©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8ru": {
    "type": "_sbcs",
    "chars": "─│┌┐└┘├┤┬┴┼▀▄█▌▐░▒▓⌠■∙√≈≤≥ ⌡°²·÷═║╒ёє╔ії╗╘╙╚╛ґў╞╟╠╡ЁЄ╣ІЇ╦╧╨╩╪ҐЎ©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "koi8t": {
    "type": "_sbcs",
    "chars": "қғ‚Ғ„…†‡�‰ҳ‹ҲҷҶ�Қ‘’“”•–—�™�›�����ӯӮё¤ӣ¦§���«¬­®�°±²Ё�Ӣ¶·�№�»���©юабцдефгхийклмнопярстужвьызшэщчъЮАБЦДЕФГХИЙКЛМНОПЯРСТУЖВЬЫЗШЭЩЧЪ"
  },
  "armscii8": {
    "type": "_sbcs",
    "chars": " �և։)(»«—.՝,-֊…՜՛՞ԱաԲբԳգԴդԵեԶզԷէԸըԹթԺժԻիԼլԽխԾծԿկՀհՁձՂղՃճՄմՅյՆնՇշՈոՉչՊպՋջՌռՍսՎվՏտՐրՑցՒւՓփՔքՕօՖֆ՚�"
  },
  "rk1048": {
    "type": "_sbcs",
    "chars": "ЂЃ‚ѓ„…†‡€‰Љ‹ЊҚҺЏђ‘’“”•–—�™љ›њқһџ ҰұӘ¤Ө¦§Ё©Ғ«¬­®Ү°±Ііөµ¶·ё№ғ»әҢңүАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "tcvn": {
    "type": "_sbcs",
    "chars": "\u0000ÚỤ\u0003ỪỬỮ\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010ỨỰỲỶỸÝỴ\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~ÀẢÃÁẠẶẬÈẺẼÉẸỆÌỈĨÍỊÒỎÕÓỌỘỜỞỠỚỢÙỦŨ ĂÂÊÔƠƯĐăâêôơưđẶ̀̀̉̃́àảãáạẲằẳẵắẴẮẦẨẪẤỀặầẩẫấậèỂẻẽéẹềểễếệìỉỄẾỒĩíịòỔỏõóọồổỗốộờởỡớợùỖủũúụừửữứựỳỷỹýỵỐ"
  },
  "georgianacademy": {
    "type": "_sbcs",
    "chars": "‚ƒ„…†‡ˆ‰Š‹Œ‘’“”•–—˜™š›œŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿აბგდევზთიკლმნოპჟრსტუფქღყშჩცძწჭხჯჰჱჲჳჴჵჶçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "georgianps": {
    "type": "_sbcs",
    "chars": "‚ƒ„…†‡ˆ‰Š‹Œ‘’“”•–—˜™š›œŸ ¡¢£¤¥¦§¨©ª«¬­®¯°±²³´µ¶·¸¹º»¼½¾¿აბგდევზჱთიკლმნჲოპჟრსტჳუფქღყშჩცძწჭხჴჯჰჵæçèéêëìíîïðñòóôõö÷øùúûüýþÿ"
  },
  "pt154": {
    "type": "_sbcs",
    "chars": "ҖҒӮғ„…ҶҮҲүҠӢҢҚҺҸҗ‘’“”•–—ҳҷҡӣңқһҹ ЎўЈӨҘҰ§Ё©Ә«¬ӯ®Ҝ°ұІіҙө¶·ё№ә»јҪҫҝАБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя"
  },
  "viscii": {
    "type": "_sbcs",
    "chars": "\u0000\u0001Ẳ\u0003\u0004ẴẪ\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013Ỷ\u0015\u0016\u0017\u0018Ỹ\u001a\u001b\u001c\u001dỴ\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~ẠẮẰẶẤẦẨẬẼẸẾỀỂỄỆỐỒỔỖỘỢỚỜỞỊỎỌỈỦŨỤỲÕắằặấầẩậẽẹếềểễệốồổỗỠƠộờởịỰỨỪỬơớƯÀÁÂÃẢĂẳẵÈÉÊẺÌÍĨỳĐứÒÓÔạỷừửÙÚỹỵÝỡưàáâãảăữẫèéêẻìíĩỉđựòóôõỏọụùúũủýợỮ"
  },
  "iso646cn": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#¥%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}‾��������������������������������������������������������������������������������������������������������������������������������"
  },
  "iso646jp": {
    "type": "_sbcs",
    "chars": "\u0000\u0001\u0002\u0003\u0004\u0005\u0006\u0007\b\t\n\u000b\f\r\u000e\u000f\u0010\u0011\u0012\u0013\u0014\u0015\u0016\u0017\u0018\u0019\u001a\u001b\u001c\u001d\u001e\u001f !\"#$%&'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[¥]^_`abcdefghijklmnopqrstuvwxyz{|}‾��������������������������������������������������������������������������������������������������������������������������������"
  },
  "hproman8": {
    "type": "_sbcs",
    "chars": " ÀÂÈÊËÎÏ´ˋˆ¨˜ÙÛ₤¯Ýý°ÇçÑñ¡¿¤£¥§ƒ¢âêôûáéóúàèòùäëöüÅîØÆåíøæÄìÖÜÉïßÔÁÃãÐðÍÌÓÒÕõŠšÚŸÿÞþ·µ¶¾—¼½ªº«■»±�"
  },
  "macintosh": {
    "type": "_sbcs",
    "chars": "ÄÅÇÉÑÖÜáàâäãåçéèêëíìîïñóòôöõúùûü†°¢£§•¶ß®©™´¨≠ÆØ∞±≤≥¥µ∂∑∏π∫ªºΩæø¿¡¬√ƒ≈∆«»… ÀÃÕŒœ–—“”‘’÷◊ÿŸ⁄¤‹›ﬁﬂ‡·‚„‰ÂÊÁËÈÍÎÏÌÓÔ�ÒÚÛÙıˆ˜¯˘˙˚¸˝˛ˇ"
  },
  "ascii": {
    "type": "_sbcs",
    "chars": "��������������������������������������������������������������������������������������������������������������������������������"
  },
  "tis620": {
    "type": "_sbcs",
    "chars": "���������������������������������กขฃคฅฆงจฉชซฌญฎฏฐฑฒณดตถทธนบปผฝพฟภมยรฤลฦวศษสหฬอฮฯะัาำิีึืฺุู����฿เแโใไๅๆ็่้๊๋์ํ๎๏๐๑๒๓๔๕๖๗๘๙๚๛����"
  }
}

/***/ }),

/***/ 99900:
/***/ (function(module) {

"use strict";


// Manually added data to be used by sbcs codec in addition to generated one.

module.exports = {
    // Not supported by iconv, not sure why.
    "10029": "maccenteuro",
    "maccenteuro": {
        "type": "_sbcs",
        "chars": "ÄĀāÉĄÖÜáąČäčĆćéŹźĎíďĒēĖóėôöõúĚěü†°Ę£§•¶ß®©™ę¨≠ģĮįĪ≤≥īĶ∂∑łĻļĽľĹĺŅņŃ¬√ńŇ∆«»… ňŐÕőŌ–—“”‘’÷◊ōŔŕŘ‹›řŖŗŠ‚„šŚśÁŤťÍŽžŪÓÔūŮÚůŰűŲųÝýķŻŁżĢˇ"
    },

    "808": "cp808",
    "ibm808": "cp808",
    "cp808": {
        "type": "_sbcs",
        "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмноп░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀рстуфхцчшщъыьэюяЁёЄєЇїЎў°∙·√№€■ "
    },

    "mik": {
        "type": "_sbcs",
        "chars": "АБВГДЕЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯабвгдежзийклмнопрстуфхцчшщъыьэюя└┴┬├─┼╣║╚╔╩╦╠═╬┐░▒▓│┤№§╗╝┘┌█▄▌▐▀αßΓπΣσµτΦΘΩδ∞φε∩≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ "
    },

    "cp720": {
        "type": "_sbcs",
        "chars": "\x80\x81éâ\x84à\x86çêëèïî\x8d\x8e\x8f\x90\u0651\u0652ô¤ـûùءآأؤ£إئابةتثجحخدذرزسشص«»░▒▓│┤╡╢╖╕╣║╗╝╜╛┐└┴┬├─┼╞╟╚╔╩╦╠═╬╧╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀ضطظعغفµقكلمنهوىي≡\u064b\u064c\u064d\u064e\u064f\u0650≈°∙·√ⁿ²■\u00a0"
    },

    // Aliases of generated encodings.
    "ascii8bit": "ascii",
    "usascii": "ascii",
    "ansix34": "ascii",
    "ansix341968": "ascii",
    "ansix341986": "ascii",
    "csascii": "ascii",
    "cp367": "ascii",
    "ibm367": "ascii",
    "isoir6": "ascii",
    "iso646us": "ascii",
    "iso646irv": "ascii",
    "us": "ascii",

    "latin1": "iso88591",
    "latin2": "iso88592",
    "latin3": "iso88593",
    "latin4": "iso88594",
    "latin5": "iso88599",
    "latin6": "iso885910",
    "latin7": "iso885913",
    "latin8": "iso885914",
    "latin9": "iso885915",
    "latin10": "iso885916",

    "csisolatin1": "iso88591",
    "csisolatin2": "iso88592",
    "csisolatin3": "iso88593",
    "csisolatin4": "iso88594",
    "csisolatincyrillic": "iso88595",
    "csisolatinarabic": "iso88596",
    "csisolatingreek" : "iso88597",
    "csisolatinhebrew": "iso88598",
    "csisolatin5": "iso88599",
    "csisolatin6": "iso885910",

    "l1": "iso88591",
    "l2": "iso88592",
    "l3": "iso88593",
    "l4": "iso88594",
    "l5": "iso88599",
    "l6": "iso885910",
    "l7": "iso885913",
    "l8": "iso885914",
    "l9": "iso885915",
    "l10": "iso885916",

    "isoir14": "iso646jp",
    "isoir57": "iso646cn",
    "isoir100": "iso88591",
    "isoir101": "iso88592",
    "isoir109": "iso88593",
    "isoir110": "iso88594",
    "isoir144": "iso88595",
    "isoir127": "iso88596",
    "isoir126": "iso88597",
    "isoir138": "iso88598",
    "isoir148": "iso88599",
    "isoir157": "iso885910",
    "isoir166": "tis620",
    "isoir179": "iso885913",
    "isoir199": "iso885914",
    "isoir203": "iso885915",
    "isoir226": "iso885916",

    "cp819": "iso88591",
    "ibm819": "iso88591",

    "cyrillic": "iso88595",

    "arabic": "iso88596",
    "arabic8": "iso88596",
    "ecma114": "iso88596",
    "asmo708": "iso88596",

    "greek" : "iso88597",
    "greek8" : "iso88597",
    "ecma118" : "iso88597",
    "elot928" : "iso88597",

    "hebrew": "iso88598",
    "hebrew8": "iso88598",

    "turkish": "iso88599",
    "turkish8": "iso88599",

    "thai": "iso885911",
    "thai8": "iso885911",

    "celtic": "iso885914",
    "celtic8": "iso885914",
    "isoceltic": "iso885914",

    "tis6200": "tis620",
    "tis62025291": "tis620",
    "tis62025330": "tis620",

    "10000": "macroman",
    "10006": "macgreek",
    "10007": "maccyrillic",
    "10079": "maciceland",
    "10081": "macturkish",

    "cspc8codepage437": "cp437",
    "cspc775baltic": "cp775",
    "cspc850multilingual": "cp850",
    "cspcp852": "cp852",
    "cspc862latinhebrew": "cp862",
    "cpgr": "cp869",

    "msee": "cp1250",
    "mscyrl": "cp1251",
    "msansi": "cp1252",
    "msgreek": "cp1253",
    "msturk": "cp1254",
    "mshebr": "cp1255",
    "msarab": "cp1256",
    "winbaltrim": "cp1257",

    "cp20866": "koi8r",
    "20866": "koi8r",
    "ibm878": "koi8r",
    "cskoi8r": "koi8r",

    "cp21866": "koi8u",
    "21866": "koi8u",
    "ibm1168": "koi8u",

    "strk10482002": "rk1048",

    "tcvn5712": "tcvn",
    "tcvn57121": "tcvn",

    "gb198880": "iso646cn",
    "cn": "iso646cn",

    "csiso14jisc6220ro": "iso646jp",
    "jisc62201969ro": "iso646jp",
    "jp": "iso646jp",

    "cshproman8": "hproman8",
    "r8": "hproman8",
    "roman8": "hproman8",
    "xroman8": "hproman8",
    "ibm1051": "hproman8",

    "mac": "macintosh",
    "csmacintosh": "macintosh",
};



/***/ }),

/***/ 17100:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";

var Buffer = (__webpack_require__(93143).Buffer);

// Note: UTF16-LE (or UCS2) codec is Node.js native. See encodings/internal.js

// == UTF16-BE codec. ==========================================================

exports.utf16be = Utf16BECodec;
function Utf16BECodec() {
}

Utf16BECodec.prototype.encoder = Utf16BEEncoder;
Utf16BECodec.prototype.decoder = Utf16BEDecoder;
Utf16BECodec.prototype.bomAware = true;


// -- Encoding

function Utf16BEEncoder() {
}

Utf16BEEncoder.prototype.write = function(str) {
    var buf = Buffer.from(str, 'ucs2');
    for (var i = 0; i < buf.length; i += 2) {
        var tmp = buf[i]; buf[i] = buf[i+1]; buf[i+1] = tmp;
    }
    return buf;
}

Utf16BEEncoder.prototype.end = function() {
}


// -- Decoding

function Utf16BEDecoder() {
    this.overflowByte = -1;
}

Utf16BEDecoder.prototype.write = function(buf) {
    if (buf.length == 0)
        return '';

    var buf2 = Buffer.alloc(buf.length + 1),
        i = 0, j = 0;

    if (this.overflowByte !== -1) {
        buf2[0] = buf[0];
        buf2[1] = this.overflowByte;
        i = 1; j = 2;
    }

    for (; i < buf.length-1; i += 2, j+= 2) {
        buf2[j] = buf[i+1];
        buf2[j+1] = buf[i];
    }

    this.overflowByte = (i == buf.length-1) ? buf[buf.length-1] : -1;

    return buf2.slice(0, j).toString('ucs2');
}

Utf16BEDecoder.prototype.end = function() {
    this.overflowByte = -1;
}


// == UTF-16 codec =============================================================
// Decoder chooses automatically from UTF-16LE and UTF-16BE using BOM and space-based heuristic.
// Defaults to UTF-16LE, as it's prevalent and default in Node.
// http://en.wikipedia.org/wiki/UTF-16 and http://encoding.spec.whatwg.org/#utf-16le
// Decoder default can be changed: iconv.decode(buf, 'utf16', {defaultEncoding: 'utf-16be'});

// Encoder uses UTF-16LE and prepends BOM (which can be overridden with addBOM: false).

exports.utf16 = Utf16Codec;
function Utf16Codec(codecOptions, iconv) {
    this.iconv = iconv;
}

Utf16Codec.prototype.encoder = Utf16Encoder;
Utf16Codec.prototype.decoder = Utf16Decoder;


// -- Encoding (pass-through)

function Utf16Encoder(options, codec) {
    options = options || {};
    if (options.addBOM === undefined)
        options.addBOM = true;
    this.encoder = codec.iconv.getEncoder('utf-16le', options);
}

Utf16Encoder.prototype.write = function(str) {
    return this.encoder.write(str);
}

Utf16Encoder.prototype.end = function() {
    return this.encoder.end();
}


// -- Decoding

function Utf16Decoder(options, codec) {
    this.decoder = null;
    this.initialBufs = [];
    this.initialBufsLen = 0;

    this.options = options || {};
    this.iconv = codec.iconv;
}

Utf16Decoder.prototype.write = function(buf) {
    if (!this.decoder) {
        // Codec is not chosen yet. Accumulate initial bytes.
        this.initialBufs.push(buf);
        this.initialBufsLen += buf.length;
        
        if (this.initialBufsLen < 16) // We need more bytes to use space heuristic (see below)
            return '';

        // We have enough bytes -> detect endianness.
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.write(buf);
}

Utf16Decoder.prototype.end = function() {
    if (!this.decoder) {
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        var trail = this.decoder.end();
        if (trail)
            resStr += trail;

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }
    return this.decoder.end();
}

function detectEncoding(bufs, defaultEncoding) {
    var b = [];
    var charsProcessed = 0;
    var asciiCharsLE = 0, asciiCharsBE = 0; // Number of ASCII chars when decoded as LE or BE.

    outer_loop:
    for (var i = 0; i < bufs.length; i++) {
        var buf = bufs[i];
        for (var j = 0; j < buf.length; j++) {
            b.push(buf[j]);
            if (b.length === 2) {
                if (charsProcessed === 0) {
                    // Check BOM first.
                    if (b[0] === 0xFF && b[1] === 0xFE) return 'utf-16le';
                    if (b[0] === 0xFE && b[1] === 0xFF) return 'utf-16be';
                }

                if (b[0] === 0 && b[1] !== 0) asciiCharsBE++;
                if (b[0] !== 0 && b[1] === 0) asciiCharsLE++;

                b.length = 0;
                charsProcessed++;

                if (charsProcessed >= 100) {
                    break outer_loop;
                }
            }
        }
    }

    // Make decisions.
    // Most of the time, the content has ASCII chars (U+00**), but the opposite (U+**00) is uncommon.
    // So, we count ASCII as if it was LE or BE, and decide from that.
    if (asciiCharsBE > asciiCharsLE) return 'utf-16be';
    if (asciiCharsBE < asciiCharsLE) return 'utf-16le';

    // Couldn't decide (likely all zeros or not enough data).
    return defaultEncoding || 'utf-16le';
}




/***/ }),

/***/ 24162:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


var Buffer = (__webpack_require__(93143).Buffer);

// == UTF32-LE/BE codec. ==========================================================

exports._utf32 = Utf32Codec;

function Utf32Codec(codecOptions, iconv) {
    this.iconv = iconv;
    this.bomAware = true;
    this.isLE = codecOptions.isLE;
}

exports.utf32le = { type: '_utf32', isLE: true };
exports.utf32be = { type: '_utf32', isLE: false };

// Aliases
exports.ucs4le = 'utf32le';
exports.ucs4be = 'utf32be';

Utf32Codec.prototype.encoder = Utf32Encoder;
Utf32Codec.prototype.decoder = Utf32Decoder;

// -- Encoding

function Utf32Encoder(options, codec) {
    this.isLE = codec.isLE;
    this.highSurrogate = 0;
}

Utf32Encoder.prototype.write = function(str) {
    var src = Buffer.from(str, 'ucs2');
    var dst = Buffer.alloc(src.length * 2);
    var write32 = this.isLE ? dst.writeUInt32LE : dst.writeUInt32BE;
    var offset = 0;

    for (var i = 0; i < src.length; i += 2) {
        var code = src.readUInt16LE(i);
        var isHighSurrogate = (0xD800 <= code && code < 0xDC00);
        var isLowSurrogate = (0xDC00 <= code && code < 0xE000);

        if (this.highSurrogate) {
            if (isHighSurrogate || !isLowSurrogate) {
                // There shouldn't be two high surrogates in a row, nor a high surrogate which isn't followed by a low
                // surrogate. If this happens, keep the pending high surrogate as a stand-alone semi-invalid character
                // (technically wrong, but expected by some applications, like Windows file names).
                write32.call(dst, this.highSurrogate, offset);
                offset += 4;
            }
            else {
                // Create 32-bit value from high and low surrogates;
                var codepoint = (((this.highSurrogate - 0xD800) << 10) | (code - 0xDC00)) + 0x10000;

                write32.call(dst, codepoint, offset);
                offset += 4;
                this.highSurrogate = 0;

                continue;
            }
        }

        if (isHighSurrogate)
            this.highSurrogate = code;
        else {
            // Even if the current character is a low surrogate, with no previous high surrogate, we'll
            // encode it as a semi-invalid stand-alone character for the same reasons expressed above for
            // unpaired high surrogates.
            write32.call(dst, code, offset);
            offset += 4;
            this.highSurrogate = 0;
        }
    }

    if (offset < dst.length)
        dst = dst.slice(0, offset);

    return dst;
};

Utf32Encoder.prototype.end = function() {
    // Treat any leftover high surrogate as a semi-valid independent character.
    if (!this.highSurrogate)
        return;

    var buf = Buffer.alloc(4);

    if (this.isLE)
        buf.writeUInt32LE(this.highSurrogate, 0);
    else
        buf.writeUInt32BE(this.highSurrogate, 0);

    this.highSurrogate = 0;

    return buf;
};

// -- Decoding

function Utf32Decoder(options, codec) {
    this.isLE = codec.isLE;
    this.badChar = codec.iconv.defaultCharUnicode.charCodeAt(0);
    this.overflow = [];
}

Utf32Decoder.prototype.write = function(src) {
    if (src.length === 0)
        return '';

    var i = 0;
    var codepoint = 0;
    var dst = Buffer.alloc(src.length + 4);
    var offset = 0;
    var isLE = this.isLE;
    var overflow = this.overflow;
    var badChar = this.badChar;

    if (overflow.length > 0) {
        for (; i < src.length && overflow.length < 4; i++)
            overflow.push(src[i]);
        
        if (overflow.length === 4) {
            // NOTE: codepoint is a signed int32 and can be negative.
            // NOTE: We copied this block from below to help V8 optimize it (it works with array, not buffer).
            if (isLE) {
                codepoint = overflow[i] | (overflow[i+1] << 8) | (overflow[i+2] << 16) | (overflow[i+3] << 24);
            } else {
                codepoint = overflow[i+3] | (overflow[i+2] << 8) | (overflow[i+1] << 16) | (overflow[i] << 24);
            }
            overflow.length = 0;

            offset = _writeCodepoint(dst, offset, codepoint, badChar);
        }
    }

    // Main loop. Should be as optimized as possible.
    for (; i < src.length - 3; i += 4) {
        // NOTE: codepoint is a signed int32 and can be negative.
        if (isLE) {
            codepoint = src[i] | (src[i+1] << 8) | (src[i+2] << 16) | (src[i+3] << 24);
        } else {
            codepoint = src[i+3] | (src[i+2] << 8) | (src[i+1] << 16) | (src[i] << 24);
        }
        offset = _writeCodepoint(dst, offset, codepoint, badChar);
    }

    // Keep overflowing bytes.
    for (; i < src.length; i++) {
        overflow.push(src[i]);
    }

    return dst.slice(0, offset).toString('ucs2');
};

function _writeCodepoint(dst, offset, codepoint, badChar) {
    // NOTE: codepoint is signed int32 and can be negative. We keep it that way to help V8 with optimizations.
    if (codepoint < 0 || codepoint > 0x10FFFF) {
        // Not a valid Unicode codepoint
        codepoint = badChar;
    } 

    // Ephemeral Planes: Write high surrogate.
    if (codepoint >= 0x10000) {
        codepoint -= 0x10000;

        var high = 0xD800 | (codepoint >> 10);
        dst[offset++] = high & 0xff;
        dst[offset++] = high >> 8;

        // Low surrogate is written below.
        var codepoint = 0xDC00 | (codepoint & 0x3FF);
    }

    // Write BMP char or low surrogate.
    dst[offset++] = codepoint & 0xff;
    dst[offset++] = codepoint >> 8;

    return offset;
};

Utf32Decoder.prototype.end = function() {
    this.overflow.length = 0;
};

// == UTF-32 Auto codec =============================================================
// Decoder chooses automatically from UTF-32LE and UTF-32BE using BOM and space-based heuristic.
// Defaults to UTF-32LE. http://en.wikipedia.org/wiki/UTF-32
// Encoder/decoder default can be changed: iconv.decode(buf, 'utf32', {defaultEncoding: 'utf-32be'});

// Encoder prepends BOM (which can be overridden with (addBOM: false}).

exports.utf32 = Utf32AutoCodec;
exports.ucs4 = 'utf32';

function Utf32AutoCodec(options, iconv) {
    this.iconv = iconv;
}

Utf32AutoCodec.prototype.encoder = Utf32AutoEncoder;
Utf32AutoCodec.prototype.decoder = Utf32AutoDecoder;

// -- Encoding

function Utf32AutoEncoder(options, codec) {
    options = options || {};

    if (options.addBOM === undefined)
        options.addBOM = true;

    this.encoder = codec.iconv.getEncoder(options.defaultEncoding || 'utf-32le', options);
}

Utf32AutoEncoder.prototype.write = function(str) {
    return this.encoder.write(str);
};

Utf32AutoEncoder.prototype.end = function() {
    return this.encoder.end();
};

// -- Decoding

function Utf32AutoDecoder(options, codec) {
    this.decoder = null;
    this.initialBufs = [];
    this.initialBufsLen = 0;
    this.options = options || {};
    this.iconv = codec.iconv;
}

Utf32AutoDecoder.prototype.write = function(buf) {
    if (!this.decoder) { 
        // Codec is not chosen yet. Accumulate initial bytes.
        this.initialBufs.push(buf);
        this.initialBufsLen += buf.length;

        if (this.initialBufsLen < 32) // We need more bytes to use space heuristic (see below)
            return '';

        // We have enough bytes -> detect endianness.
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.write(buf);
};

Utf32AutoDecoder.prototype.end = function() {
    if (!this.decoder) {
        var encoding = detectEncoding(this.initialBufs, this.options.defaultEncoding);
        this.decoder = this.iconv.getDecoder(encoding, this.options);

        var resStr = '';
        for (var i = 0; i < this.initialBufs.length; i++)
            resStr += this.decoder.write(this.initialBufs[i]);

        var trail = this.decoder.end();
        if (trail)
            resStr += trail;

        this.initialBufs.length = this.initialBufsLen = 0;
        return resStr;
    }

    return this.decoder.end();
};

function detectEncoding(bufs, defaultEncoding) {
    var b = [];
    var charsProcessed = 0;
    var invalidLE = 0, invalidBE = 0;   // Number of invalid chars when decoded as LE or BE.
    var bmpCharsLE = 0, bmpCharsBE = 0; // Number of BMP chars when decoded as LE or BE.

    outer_loop:
    for (var i = 0; i < bufs.length; i++) {
        var buf = bufs[i];
        for (var j = 0; j < buf.length; j++) {
            b.push(buf[j]);
            if (b.length === 4) {
                if (charsProcessed === 0) {
                    // Check BOM first.
                    if (b[0] === 0xFF && b[1] === 0xFE && b[2] === 0 && b[3] === 0) {
                        return 'utf-32le';
                    }
                    if (b[0] === 0 && b[1] === 0 && b[2] === 0xFE && b[3] === 0xFF) {
                        return 'utf-32be';
                    }
                }

                if (b[0] !== 0 || b[1] > 0x10) invalidBE++;
                if (b[3] !== 0 || b[2] > 0x10) invalidLE++;

                if (b[0] === 0 && b[1] === 0 && (b[2] !== 0 || b[3] !== 0)) bmpCharsBE++;
                if ((b[0] !== 0 || b[1] !== 0) && b[2] === 0 && b[3] === 0) bmpCharsLE++;

                b.length = 0;
                charsProcessed++;

                if (charsProcessed >= 100) {
                    break outer_loop;
                }
            }
        }
    }

    // Make decisions.
    if (bmpCharsBE - invalidBE > bmpCharsLE - invalidLE)  return 'utf-32be';
    if (bmpCharsBE - invalidBE < bmpCharsLE - invalidLE)  return 'utf-32le';

    // Couldn't decide (likely all zeros or not enough data).
    return defaultEncoding || 'utf-32le';
}


/***/ }),

/***/ 11326:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";

var Buffer = (__webpack_require__(93143).Buffer);

// UTF-7 codec, according to https://tools.ietf.org/html/rfc2152
// See also below a UTF-7-IMAP codec, according to http://tools.ietf.org/html/rfc3501#section-5.1.3

exports.utf7 = Utf7Codec;
exports.unicode11utf7 = 'utf7'; // Alias UNICODE-1-1-UTF-7
function Utf7Codec(codecOptions, iconv) {
    this.iconv = iconv;
};

Utf7Codec.prototype.encoder = Utf7Encoder;
Utf7Codec.prototype.decoder = Utf7Decoder;
Utf7Codec.prototype.bomAware = true;


// -- Encoding

var nonDirectChars = /[^A-Za-z0-9'\(\),-\.\/:\? \n\r\t]+/g;

function Utf7Encoder(options, codec) {
    this.iconv = codec.iconv;
}

Utf7Encoder.prototype.write = function(str) {
    // Naive implementation.
    // Non-direct chars are encoded as "+<base64>-"; single "+" char is encoded as "+-".
    return Buffer.from(str.replace(nonDirectChars, function(chunk) {
        return "+" + (chunk === '+' ? '' : 
            this.iconv.encode(chunk, 'utf16-be').toString('base64').replace(/=+$/, '')) 
            + "-";
    }.bind(this)));
}

Utf7Encoder.prototype.end = function() {
}


// -- Decoding

function Utf7Decoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = '';
}

var base64Regex = /[A-Za-z0-9\/+]/;
var base64Chars = [];
for (var i = 0; i < 256; i++)
    base64Chars[i] = base64Regex.test(String.fromCharCode(i));

var plusChar = '+'.charCodeAt(0), 
    minusChar = '-'.charCodeAt(0),
    andChar = '&'.charCodeAt(0);

Utf7Decoder.prototype.write = function(buf) {
    var res = "", lastI = 0,
        inBase64 = this.inBase64,
        base64Accum = this.base64Accum;

    // The decoder is more involved as we must handle chunks in stream.

    for (var i = 0; i < buf.length; i++) {
        if (!inBase64) { // We're in direct mode.
            // Write direct chars until '+'
            if (buf[i] == plusChar) {
                res += this.iconv.decode(buf.slice(lastI, i), "ascii"); // Write direct chars.
                lastI = i+1;
                inBase64 = true;
            }
        } else { // We decode base64.
            if (!base64Chars[buf[i]]) { // Base64 ended.
                if (i == lastI && buf[i] == minusChar) {// "+-" -> "+"
                    res += "+";
                } else {
                    var b64str = base64Accum + this.iconv.decode(buf.slice(lastI, i), "ascii");
                    res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
                }

                if (buf[i] != minusChar) // Minus is absorbed after base64.
                    i--;

                lastI = i+1;
                inBase64 = false;
                base64Accum = '';
            }
        }
    }

    if (!inBase64) {
        res += this.iconv.decode(buf.slice(lastI), "ascii"); // Write direct chars.
    } else {
        var b64str = base64Accum + this.iconv.decode(buf.slice(lastI), "ascii");

        var canBeDecoded = b64str.length - (b64str.length % 8); // Minimal chunk: 2 quads -> 2x3 bytes -> 3 chars.
        base64Accum = b64str.slice(canBeDecoded); // The rest will be decoded in future.
        b64str = b64str.slice(0, canBeDecoded);

        res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
    }

    this.inBase64 = inBase64;
    this.base64Accum = base64Accum;

    return res;
}

Utf7Decoder.prototype.end = function() {
    var res = "";
    if (this.inBase64 && this.base64Accum.length > 0)
        res = this.iconv.decode(Buffer.from(this.base64Accum, 'base64'), "utf16-be");

    this.inBase64 = false;
    this.base64Accum = '';
    return res;
}


// UTF-7-IMAP codec.
// RFC3501 Sec. 5.1.3 Modified UTF-7 (http://tools.ietf.org/html/rfc3501#section-5.1.3)
// Differences:
//  * Base64 part is started by "&" instead of "+"
//  * Direct characters are 0x20-0x7E, except "&" (0x26)
//  * In Base64, "," is used instead of "/"
//  * Base64 must not be used to represent direct characters.
//  * No implicit shift back from Base64 (should always end with '-')
//  * String must end in non-shifted position.
//  * "-&" while in base64 is not allowed.


exports.utf7imap = Utf7IMAPCodec;
function Utf7IMAPCodec(codecOptions, iconv) {
    this.iconv = iconv;
};

Utf7IMAPCodec.prototype.encoder = Utf7IMAPEncoder;
Utf7IMAPCodec.prototype.decoder = Utf7IMAPDecoder;
Utf7IMAPCodec.prototype.bomAware = true;


// -- Encoding

function Utf7IMAPEncoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = Buffer.alloc(6);
    this.base64AccumIdx = 0;
}

Utf7IMAPEncoder.prototype.write = function(str) {
    var inBase64 = this.inBase64,
        base64Accum = this.base64Accum,
        base64AccumIdx = this.base64AccumIdx,
        buf = Buffer.alloc(str.length*5 + 10), bufIdx = 0;

    for (var i = 0; i < str.length; i++) {
        var uChar = str.charCodeAt(i);
        if (0x20 <= uChar && uChar <= 0x7E) { // Direct character or '&'.
            if (inBase64) {
                if (base64AccumIdx > 0) {
                    bufIdx += buf.write(base64Accum.slice(0, base64AccumIdx).toString('base64').replace(/\//g, ',').replace(/=+$/, ''), bufIdx);
                    base64AccumIdx = 0;
                }

                buf[bufIdx++] = minusChar; // Write '-', then go to direct mode.
                inBase64 = false;
            }

            if (!inBase64) {
                buf[bufIdx++] = uChar; // Write direct character

                if (uChar === andChar)  // Ampersand -> '&-'
                    buf[bufIdx++] = minusChar;
            }

        } else { // Non-direct character
            if (!inBase64) {
                buf[bufIdx++] = andChar; // Write '&', then go to base64 mode.
                inBase64 = true;
            }
            if (inBase64) {
                base64Accum[base64AccumIdx++] = uChar >> 8;
                base64Accum[base64AccumIdx++] = uChar & 0xFF;

                if (base64AccumIdx == base64Accum.length) {
                    bufIdx += buf.write(base64Accum.toString('base64').replace(/\//g, ','), bufIdx);
                    base64AccumIdx = 0;
                }
            }
        }
    }

    this.inBase64 = inBase64;
    this.base64AccumIdx = base64AccumIdx;

    return buf.slice(0, bufIdx);
}

Utf7IMAPEncoder.prototype.end = function() {
    var buf = Buffer.alloc(10), bufIdx = 0;
    if (this.inBase64) {
        if (this.base64AccumIdx > 0) {
            bufIdx += buf.write(this.base64Accum.slice(0, this.base64AccumIdx).toString('base64').replace(/\//g, ',').replace(/=+$/, ''), bufIdx);
            this.base64AccumIdx = 0;
        }

        buf[bufIdx++] = minusChar; // Write '-', then go to direct mode.
        this.inBase64 = false;
    }

    return buf.slice(0, bufIdx);
}


// -- Decoding

function Utf7IMAPDecoder(options, codec) {
    this.iconv = codec.iconv;
    this.inBase64 = false;
    this.base64Accum = '';
}

var base64IMAPChars = base64Chars.slice();
base64IMAPChars[','.charCodeAt(0)] = true;

Utf7IMAPDecoder.prototype.write = function(buf) {
    var res = "", lastI = 0,
        inBase64 = this.inBase64,
        base64Accum = this.base64Accum;

    // The decoder is more involved as we must handle chunks in stream.
    // It is forgiving, closer to standard UTF-7 (for example, '-' is optional at the end).

    for (var i = 0; i < buf.length; i++) {
        if (!inBase64) { // We're in direct mode.
            // Write direct chars until '&'
            if (buf[i] == andChar) {
                res += this.iconv.decode(buf.slice(lastI, i), "ascii"); // Write direct chars.
                lastI = i+1;
                inBase64 = true;
            }
        } else { // We decode base64.
            if (!base64IMAPChars[buf[i]]) { // Base64 ended.
                if (i == lastI && buf[i] == minusChar) { // "&-" -> "&"
                    res += "&";
                } else {
                    var b64str = base64Accum + this.iconv.decode(buf.slice(lastI, i), "ascii").replace(/,/g, '/');
                    res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
                }

                if (buf[i] != minusChar) // Minus may be absorbed after base64.
                    i--;

                lastI = i+1;
                inBase64 = false;
                base64Accum = '';
            }
        }
    }

    if (!inBase64) {
        res += this.iconv.decode(buf.slice(lastI), "ascii"); // Write direct chars.
    } else {
        var b64str = base64Accum + this.iconv.decode(buf.slice(lastI), "ascii").replace(/,/g, '/');

        var canBeDecoded = b64str.length - (b64str.length % 8); // Minimal chunk: 2 quads -> 2x3 bytes -> 3 chars.
        base64Accum = b64str.slice(canBeDecoded); // The rest will be decoded in future.
        b64str = b64str.slice(0, canBeDecoded);

        res += this.iconv.decode(Buffer.from(b64str, 'base64'), "utf16-be");
    }

    this.inBase64 = inBase64;
    this.base64Accum = base64Accum;

    return res;
}

Utf7IMAPDecoder.prototype.end = function() {
    var res = "";
    if (this.inBase64 && this.base64Accum.length > 0)
        res = this.iconv.decode(Buffer.from(this.base64Accum, 'base64'), "utf16-be");

    this.inBase64 = false;
    this.base64Accum = '';
    return res;
}




/***/ }),

/***/ 52331:
/***/ (function(__unused_webpack_module, exports) {

"use strict";


var BOMChar = '\uFEFF';

exports.PrependBOM = PrependBOMWrapper
function PrependBOMWrapper(encoder, options) {
    this.encoder = encoder;
    this.addBOM = true;
}

PrependBOMWrapper.prototype.write = function(str) {
    if (this.addBOM) {
        str = BOMChar + str;
        this.addBOM = false;
    }

    return this.encoder.write(str);
}

PrependBOMWrapper.prototype.end = function() {
    return this.encoder.end();
}


//------------------------------------------------------------------------------

exports.StripBOM = StripBOMWrapper;
function StripBOMWrapper(decoder, options) {
    this.decoder = decoder;
    this.pass = false;
    this.options = options || {};
}

StripBOMWrapper.prototype.write = function(buf) {
    var res = this.decoder.write(buf);
    if (this.pass || !res)
        return res;

    if (res[0] === BOMChar) {
        res = res.slice(1);
        if (typeof this.options.stripBOM === 'function')
            this.options.stripBOM();
    }

    this.pass = true;
    return res;
}

StripBOMWrapper.prototype.end = function() {
    return this.decoder.end();
}



/***/ }),

/***/ 54171:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var Buffer = (__webpack_require__(93143).Buffer);

var bomHandling = __webpack_require__(52331),
    iconv = module.exports;

// All codecs and aliases are kept here, keyed by encoding name/alias.
// They are lazy loaded in `iconv.getCodec` from `encodings/index.js`.
iconv.encodings = null;

// Characters emitted in case of error.
iconv.defaultCharUnicode = '�';
iconv.defaultCharSingleByte = '?';

// Public API.
iconv.encode = function encode(str, encoding, options) {
    str = "" + (str || ""); // Ensure string.

    var encoder = iconv.getEncoder(encoding, options);

    var res = encoder.write(str);
    var trail = encoder.end();
    
    return (trail && trail.length > 0) ? Buffer.concat([res, trail]) : res;
}

iconv.decode = function decode(buf, encoding, options) {
    if (typeof buf === 'string') {
        if (!iconv.skipDecodeWarning) {
            console.error('Iconv-lite warning: decode()-ing strings is deprecated. Refer to https://github.com/ashtuchkin/iconv-lite/wiki/Use-Buffers-when-decoding');
            iconv.skipDecodeWarning = true;
        }

        buf = Buffer.from("" + (buf || ""), "binary"); // Ensure buffer.
    }

    var decoder = iconv.getDecoder(encoding, options);

    var res = decoder.write(buf);
    var trail = decoder.end();

    return trail ? (res + trail) : res;
}

iconv.encodingExists = function encodingExists(enc) {
    try {
        iconv.getCodec(enc);
        return true;
    } catch (e) {
        return false;
    }
}

// Legacy aliases to convert functions
iconv.toEncoding = iconv.encode;
iconv.fromEncoding = iconv.decode;

// Search for a codec in iconv.encodings. Cache codec data in iconv._codecDataCache.
iconv._codecDataCache = {};
iconv.getCodec = function getCodec(encoding) {
    if (!iconv.encodings)
        iconv.encodings = __webpack_require__(26326); // Lazy load all encoding definitions.
    
    // Canonicalize encoding name: strip all non-alphanumeric chars and appended year.
    var enc = iconv._canonicalizeEncoding(encoding);

    // Traverse iconv.encodings to find actual codec.
    var codecOptions = {};
    while (true) {
        var codec = iconv._codecDataCache[enc];
        if (codec)
            return codec;

        var codecDef = iconv.encodings[enc];

        switch (typeof codecDef) {
            case "string": // Direct alias to other encoding.
                enc = codecDef;
                break;

            case "object": // Alias with options. Can be layered.
                for (var key in codecDef)
                    codecOptions[key] = codecDef[key];

                if (!codecOptions.encodingName)
                    codecOptions.encodingName = enc;
                
                enc = codecDef.type;
                break;

            case "function": // Codec itself.
                if (!codecOptions.encodingName)
                    codecOptions.encodingName = enc;

                // The codec function must load all tables and return object with .encoder and .decoder methods.
                // It'll be called only once (for each different options object).
                codec = new codecDef(codecOptions, iconv);

                iconv._codecDataCache[codecOptions.encodingName] = codec; // Save it to be reused later.
                return codec;

            default:
                throw new Error("Encoding not recognized: '" + encoding + "' (searched as: '"+enc+"')");
        }
    }
}

iconv._canonicalizeEncoding = function(encoding) {
    // Canonicalize encoding name: strip all non-alphanumeric chars and appended year.
    return (''+encoding).toLowerCase().replace(/:\d{4}$|[^0-9a-z]/g, "");
}

iconv.getEncoder = function getEncoder(encoding, options) {
    var codec = iconv.getCodec(encoding),
        encoder = new codec.encoder(options, codec);

    if (codec.bomAware && options && options.addBOM)
        encoder = new bomHandling.PrependBOM(encoder, options);

    return encoder;
}

iconv.getDecoder = function getDecoder(encoding, options) {
    var codec = iconv.getCodec(encoding),
        decoder = new codec.decoder(options, codec);

    if (codec.bomAware && !(options && options.stripBOM === false))
        decoder = new bomHandling.StripBOM(decoder, options);

    return decoder;
}

// Streaming API
// NOTE: Streaming API naturally depends on 'stream' module from Node.js. Unfortunately in browser environments this module can add
// up to 100Kb to the output bundle. To avoid unnecessary code bloat, we don't enable Streaming API in browser by default.
// If you would like to enable it explicitly, please add the following code to your app:
// > iconv.enableStreamingAPI(require('stream'));
iconv.enableStreamingAPI = function enableStreamingAPI(stream_module) {
    if (iconv.supportsStreams)
        return;

    // Dependency-inject stream module to create IconvLite stream classes.
    var streams = __webpack_require__(34506)(stream_module);

    // Not public API yet, but expose the stream classes.
    iconv.IconvLiteEncoderStream = streams.IconvLiteEncoderStream;
    iconv.IconvLiteDecoderStream = streams.IconvLiteDecoderStream;

    // Streaming API.
    iconv.encodeStream = function encodeStream(encoding, options) {
        return new iconv.IconvLiteEncoderStream(iconv.getEncoder(encoding, options), options);
    }

    iconv.decodeStream = function decodeStream(encoding, options) {
        return new iconv.IconvLiteDecoderStream(iconv.getDecoder(encoding, options), options);
    }

    iconv.supportsStreams = true;
}

// Enable Streaming API automatically if 'stream' module is available and non-empty (the majority of environments).
var stream_module;
try {
    stream_module = __webpack_require__(16403);
} catch (e) {}

if (stream_module && stream_module.Transform) {
    iconv.enableStreamingAPI(stream_module);

} else {
    // In rare cases where 'stream' module is not available by default, throw a helpful exception.
    iconv.encodeStream = iconv.decodeStream = function() {
        throw new Error("iconv-lite Streaming API is not enabled. Use iconv.enableStreamingAPI(require('stream')); to enable it.");
    };
}

if (false) {}


/***/ }),

/***/ 34506:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var Buffer = (__webpack_require__(93143).Buffer);

// NOTE: Due to 'stream' module being pretty large (~100Kb, significant in browser environments), 
// we opt to dependency-inject it instead of creating a hard dependency.
module.exports = function(stream_module) {
    var Transform = stream_module.Transform;

    // == Encoder stream =======================================================

    function IconvLiteEncoderStream(conv, options) {
        this.conv = conv;
        options = options || {};
        options.decodeStrings = false; // We accept only strings, so we don't need to decode them.
        Transform.call(this, options);
    }

    IconvLiteEncoderStream.prototype = Object.create(Transform.prototype, {
        constructor: { value: IconvLiteEncoderStream }
    });

    IconvLiteEncoderStream.prototype._transform = function(chunk, encoding, done) {
        if (typeof chunk != 'string')
            return done(new Error("Iconv encoding stream needs strings as its input."));
        try {
            var res = this.conv.write(chunk);
            if (res && res.length) this.push(res);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteEncoderStream.prototype._flush = function(done) {
        try {
            var res = this.conv.end();
            if (res && res.length) this.push(res);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteEncoderStream.prototype.collect = function(cb) {
        var chunks = [];
        this.on('error', cb);
        this.on('data', function(chunk) { chunks.push(chunk); });
        this.on('end', function() {
            cb(null, Buffer.concat(chunks));
        });
        return this;
    }


    // == Decoder stream =======================================================

    function IconvLiteDecoderStream(conv, options) {
        this.conv = conv;
        options = options || {};
        options.encoding = this.encoding = 'utf8'; // We output strings.
        Transform.call(this, options);
    }

    IconvLiteDecoderStream.prototype = Object.create(Transform.prototype, {
        constructor: { value: IconvLiteDecoderStream }
    });

    IconvLiteDecoderStream.prototype._transform = function(chunk, encoding, done) {
        if (!Buffer.isBuffer(chunk) && !(chunk instanceof Uint8Array))
            return done(new Error("Iconv decoding stream needs buffers as its input."));
        try {
            var res = this.conv.write(chunk);
            if (res && res.length) this.push(res, this.encoding);
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteDecoderStream.prototype._flush = function(done) {
        try {
            var res = this.conv.end();
            if (res && res.length) this.push(res, this.encoding);                
            done();
        }
        catch (e) {
            done(e);
        }
    }

    IconvLiteDecoderStream.prototype.collect = function(cb) {
        var res = '';
        this.on('error', cb);
        this.on('data', function(chunk) { res += chunk; });
        this.on('end', function() {
            cb(null, res);
        });
        return this;
    }

    return {
        IconvLiteEncoderStream: IconvLiteEncoderStream,
        IconvLiteDecoderStream: IconvLiteDecoderStream,
    };
};


/***/ }),

/***/ 89029:
/***/ (function(__unused_webpack_module, exports) {

/*! ieee754. BSD-3-Clause License. Feross Aboukhadijeh <https://feross.org/opensource> */
exports.read = function (buffer, offset, isLE, mLen, nBytes) {
  var e, m
  var eLen = (nBytes * 8) - mLen - 1
  var eMax = (1 << eLen) - 1
  var eBias = eMax >> 1
  var nBits = -7
  var i = isLE ? (nBytes - 1) : 0
  var d = isLE ? -1 : 1
  var s = buffer[offset + i]

  i += d

  e = s & ((1 << (-nBits)) - 1)
  s >>= (-nBits)
  nBits += eLen
  for (; nBits > 0; e = (e * 256) + buffer[offset + i], i += d, nBits -= 8) {}

  m = e & ((1 << (-nBits)) - 1)
  e >>= (-nBits)
  nBits += mLen
  for (; nBits > 0; m = (m * 256) + buffer[offset + i], i += d, nBits -= 8) {}

  if (e === 0) {
    e = 1 - eBias
  } else if (e === eMax) {
    return m ? NaN : ((s ? -1 : 1) * Infinity)
  } else {
    m = m + Math.pow(2, mLen)
    e = e - eBias
  }
  return (s ? -1 : 1) * m * Math.pow(2, e - mLen)
}

exports.write = function (buffer, value, offset, isLE, mLen, nBytes) {
  var e, m, c
  var eLen = (nBytes * 8) - mLen - 1
  var eMax = (1 << eLen) - 1
  var eBias = eMax >> 1
  var rt = (mLen === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0)
  var i = isLE ? 0 : (nBytes - 1)
  var d = isLE ? 1 : -1
  var s = value < 0 || (value === 0 && 1 / value < 0) ? 1 : 0

  value = Math.abs(value)

  if (isNaN(value) || value === Infinity) {
    m = isNaN(value) ? 1 : 0
    e = eMax
  } else {
    e = Math.floor(Math.log(value) / Math.LN2)
    if (value * (c = Math.pow(2, -e)) < 1) {
      e--
      c *= 2
    }
    if (e + eBias >= 1) {
      value += rt / c
    } else {
      value += rt * Math.pow(2, 1 - eBias)
    }
    if (value * c >= 2) {
      e++
      c /= 2
    }

    if (e + eBias >= eMax) {
      m = 0
      e = eMax
    } else if (e + eBias >= 1) {
      m = ((value * c) - 1) * Math.pow(2, mLen)
      e = e + eBias
    } else {
      m = value * Math.pow(2, eBias - 1) * Math.pow(2, mLen)
      e = 0
    }
  }

  for (; mLen >= 8; buffer[offset + i] = m & 0xff, i += d, m /= 256, mLen -= 8) {}

  e = (e << mLen) | m
  eLen += mLen
  for (; eLen > 0; buffer[offset + i] = e & 0xff, i += d, e /= 256, eLen -= 8) {}

  buffer[offset + i - d] |= s * 128
}


/***/ }),

/***/ 89784:
/***/ (function(module) {

if (typeof Object.create === 'function') {
  // implementation from standard node.js 'util' module
  module.exports = function inherits(ctor, superCtor) {
    if (superCtor) {
      ctor.super_ = superCtor
      ctor.prototype = Object.create(superCtor.prototype, {
        constructor: {
          value: ctor,
          enumerable: false,
          writable: true,
          configurable: true
        }
      })
    }
  };
} else {
  // old school shim for old browsers
  module.exports = function inherits(ctor, superCtor) {
    if (superCtor) {
      ctor.super_ = superCtor
      var TempCtor = function () {}
      TempCtor.prototype = superCtor.prototype
      ctor.prototype = new TempCtor()
      ctor.prototype.constructor = ctor
    }
  }
}


/***/ }),

/***/ 67906:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var hasToStringTag = __webpack_require__(26626)();
var callBound = __webpack_require__(22774);

var $toString = callBound('Object.prototype.toString');

/** @type {import('.')} */
var isStandardArguments = function isArguments(value) {
	if (
		hasToStringTag
		&& value
		&& typeof value === 'object'
		&& Symbol.toStringTag in value
	) {
		return false;
	}
	return $toString(value) === '[object Arguments]';
};

/** @type {import('.')} */
var isLegacyArguments = function isArguments(value) {
	if (isStandardArguments(value)) {
		return true;
	}
	return value !== null
		&& typeof value === 'object'
		&& 'length' in value
		&& typeof value.length === 'number'
		&& value.length >= 0
		&& $toString(value) !== '[object Array]'
		&& 'callee' in value
		&& $toString(value.callee) === '[object Function]';
};

var supportsStandardArguments = (function () {
	return isStandardArguments(arguments);
}());

// @ts-expect-error TODO make this not error
isStandardArguments.isLegacyArguments = isLegacyArguments; // for tests

/** @type {import('.')} */
module.exports = supportsStandardArguments ? isStandardArguments : isLegacyArguments;


/***/ }),

/***/ 3746:
/***/ (function(module) {

"use strict";


var fnToStr = Function.prototype.toString;
var reflectApply = typeof Reflect === 'object' && Reflect !== null && Reflect.apply;
var badArrayLike;
var isCallableMarker;
if (typeof reflectApply === 'function' && typeof Object.defineProperty === 'function') {
	try {
		badArrayLike = Object.defineProperty({}, 'length', {
			get: function () {
				throw isCallableMarker;
			}
		});
		isCallableMarker = {};
		// eslint-disable-next-line no-throw-literal
		reflectApply(function () { throw 42; }, null, badArrayLike);
	} catch (_) {
		if (_ !== isCallableMarker) {
			reflectApply = null;
		}
	}
} else {
	reflectApply = null;
}

var constructorRegex = /^\s*class\b/;
var isES6ClassFn = function isES6ClassFunction(value) {
	try {
		var fnStr = fnToStr.call(value);
		return constructorRegex.test(fnStr);
	} catch (e) {
		return false; // not a function
	}
};

var tryFunctionObject = function tryFunctionToStr(value) {
	try {
		if (isES6ClassFn(value)) { return false; }
		fnToStr.call(value);
		return true;
	} catch (e) {
		return false;
	}
};
var toStr = Object.prototype.toString;
var objectClass = '[object Object]';
var fnClass = '[object Function]';
var genClass = '[object GeneratorFunction]';
var ddaClass = '[object HTMLAllCollection]'; // IE 11
var ddaClass2 = '[object HTML document.all class]';
var ddaClass3 = '[object HTMLCollection]'; // IE 9-10
var hasToStringTag = typeof Symbol === 'function' && !!Symbol.toStringTag; // better: use `has-tostringtag`

var isIE68 = !(0 in [,]); // eslint-disable-line no-sparse-arrays, comma-spacing

var isDDA = function isDocumentDotAll() { return false; };
if (typeof document === 'object') {
	// Firefox 3 canonicalizes DDA to undefined when it's not accessed directly
	var all = document.all;
	if (toStr.call(all) === toStr.call(document.all)) {
		isDDA = function isDocumentDotAll(value) {
			/* globals document: false */
			// in IE 6-8, typeof document.all is "object" and it's truthy
			if ((isIE68 || !value) && (typeof value === 'undefined' || typeof value === 'object')) {
				try {
					var str = toStr.call(value);
					return (
						str === ddaClass
						|| str === ddaClass2
						|| str === ddaClass3 // opera 12.16
						|| str === objectClass // IE 6-8
					) && value('') == null; // eslint-disable-line eqeqeq
				} catch (e) { /**/ }
			}
			return false;
		};
	}
}

module.exports = reflectApply
	? function isCallable(value) {
		if (isDDA(value)) { return true; }
		if (!value) { return false; }
		if (typeof value !== 'function' && typeof value !== 'object') { return false; }
		try {
			reflectApply(value, null, badArrayLike);
		} catch (e) {
			if (e !== isCallableMarker) { return false; }
		}
		return !isES6ClassFn(value) && tryFunctionObject(value);
	}
	: function isCallable(value) {
		if (isDDA(value)) { return true; }
		if (!value) { return false; }
		if (typeof value !== 'function' && typeof value !== 'object') { return false; }
		if (hasToStringTag) { return tryFunctionObject(value); }
		if (isES6ClassFn(value)) { return false; }
		var strClass = toStr.call(value);
		if (strClass !== fnClass && strClass !== genClass && !(/^\[object HTML/).test(strClass)) { return false; }
		return tryFunctionObject(value);
	};


/***/ }),

/***/ 71230:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var callBound = __webpack_require__(22774);

var getDay = callBound('Date.prototype.getDay');
/** @type {import('.')} */
var tryDateObject = function tryDateGetDayCall(value) {
	try {
		getDay(value);
		return true;
	} catch (e) {
		return false;
	}
};

/** @type {(value: unknown) => string} */
var toStr = callBound('Object.prototype.toString');
var dateClass = '[object Date]';
var hasToStringTag = __webpack_require__(26626)();

/** @type {import('.')} */
module.exports = function isDateObject(value) {
	if (typeof value !== 'object' || value === null) {
		return false;
	}
	return hasToStringTag ? tryDateObject(value) : toStr(value) === dateClass;
};


/***/ }),

/***/ 44610:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var toStr = Object.prototype.toString;
var fnToStr = Function.prototype.toString;
var isFnRegex = /^\s*(?:function)?\*/;
var hasToStringTag = __webpack_require__(26626)();
var getProto = Object.getPrototypeOf;
var getGeneratorFunc = function () { // eslint-disable-line consistent-return
	if (!hasToStringTag) {
		return false;
	}
	try {
		return Function('return function*() {}')();
	} catch (e) {
	}
};
var GeneratorFunction;

module.exports = function isGeneratorFunction(fn) {
	if (typeof fn !== 'function') {
		return false;
	}
	if (isFnRegex.test(fnToStr.call(fn))) {
		return true;
	}
	if (!hasToStringTag) {
		var str = toStr.call(fn);
		return str === '[object GeneratorFunction]';
	}
	if (!getProto) {
		return false;
	}
	if (typeof GeneratorFunction === 'undefined') {
		var generatorFunc = getGeneratorFunc();
		GeneratorFunction = generatorFunc ? getProto(generatorFunc) : false;
	}
	return getProto(fn) === GeneratorFunction;
};


/***/ }),

/***/ 82621:
/***/ (function(module) {

"use strict";


/* http://www.ecma-international.org/ecma-262/6.0/#sec-number.isnan */

module.exports = function isNaN(value) {
	return value !== value;
};


/***/ }),

/***/ 7051:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var callBind = __webpack_require__(26601);
var define = __webpack_require__(77802);

var implementation = __webpack_require__(82621);
var getPolyfill = __webpack_require__(61320);
var shim = __webpack_require__(35074);

var polyfill = callBind(getPolyfill(), Number);

/* http://www.ecma-international.org/ecma-262/6.0/#sec-number.isnan */

define(polyfill, {
	getPolyfill: getPolyfill,
	implementation: implementation,
	shim: shim
});

module.exports = polyfill;


/***/ }),

/***/ 61320:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(82621);

module.exports = function getPolyfill() {
	if (Number.isNaN && Number.isNaN(NaN) && !Number.isNaN('a')) {
		return Number.isNaN;
	}
	return implementation;
};


/***/ }),

/***/ 35074:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(77802);
var getPolyfill = __webpack_require__(61320);

/* http://www.ecma-international.org/ecma-262/6.0/#sec-number.isnan */

module.exports = function shimNumberIsNaN() {
	var polyfill = getPolyfill();
	define(Number, { isNaN: polyfill }, {
		isNaN: function testIsNaN() {
			return Number.isNaN !== polyfill;
		}
	});
	return polyfill;
};


/***/ }),

/***/ 71689:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var callBound = __webpack_require__(22774);
var hasToStringTag = __webpack_require__(26626)();
var hasOwn = __webpack_require__(55215);
var gOPD = __webpack_require__(68109);

/** @type {import('.')} */
var fn;

if (hasToStringTag) {
	/** @type {(receiver: ThisParameterType<typeof RegExp.prototype.exec>, ...args: Parameters<typeof RegExp.prototype.exec>) => ReturnType<typeof RegExp.prototype.exec>} */
	var $exec = callBound('RegExp.prototype.exec');
	/** @type {object} */
	var isRegexMarker = {};

	var throwRegexMarker = function () {
		throw isRegexMarker;
	};
	/** @type {{ toString(): never, valueOf(): never, [Symbol.toPrimitive]?(): never }} */
	var badStringifier = {
		toString: throwRegexMarker,
		valueOf: throwRegexMarker
	};

	if (typeof Symbol.toPrimitive === 'symbol') {
		badStringifier[Symbol.toPrimitive] = throwRegexMarker;
	}

	/** @type {import('.')} */
	// @ts-expect-error TS can't figure out that the $exec call always throws
	// eslint-disable-next-line consistent-return
	fn = function isRegex(value) {
		if (!value || typeof value !== 'object') {
			return false;
		}

		// eslint-disable-next-line no-extra-parens
		var descriptor = /** @type {NonNullable<typeof gOPD>} */ (gOPD)(/** @type {{ lastIndex?: unknown }} */ (value), 'lastIndex');
		var hasLastIndexDataProperty = descriptor && hasOwn(descriptor, 'value');
		if (!hasLastIndexDataProperty) {
			return false;
		}

		try {
			// eslint-disable-next-line no-extra-parens
			$exec(value, /** @type {string} */ (/** @type {unknown} */ (badStringifier)));
		} catch (e) {
			return e === isRegexMarker;
		}
	};
} else {
	/** @type {(receiver: ThisParameterType<typeof Object.prototype.toString>, ...args: Parameters<typeof Object.prototype.toString>) => ReturnType<typeof Object.prototype.toString>} */
	var $toString = callBound('Object.prototype.toString');
	/** @const @type {'[object RegExp]'} */
	var regexClass = '[object RegExp]';

	/** @type {import('.')} */
	fn = function isRegex(value) {
		// In older browsers, typeof regex incorrectly returns 'function'
		if (!value || (typeof value !== 'object' && typeof value !== 'function')) {
			return false;
		}

		return $toString(value) === regexClass;
	};
}

module.exports = fn;


/***/ }),

/***/ 46094:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var whichTypedArray = __webpack_require__(43381);

/** @type {import('.')} */
module.exports = function isTypedArray(value) {
	return !!whichTypedArray(value);
};


/***/ }),

/***/ 38834:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


var _fs = __webpack_require__(48181);

var _fs2 = _interopRequireDefault(_fs);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

var tags = __webpack_require__(9240);

/*
 unsignedByte,
 asciiStrings,
 unsignedShort,
 unsignedLong,
 unsignedRational,
 signedByte,
 undefined,
 signedShort,
 signedLong,
 signedRational,
 singleFloat,
 doubleFloat
 */
var bytes = [0, 1, 1, 2, 4, 8, 1, 1, 2, 4, 8, 4, 8];
var SOIMarkerLength = 2;
var JPEGSOIMarker = 0xffd8;
var TIFFINTEL = 0x4949;
var TIFFMOTOROLA = 0x4d4d;
var APPMarkerLength = 2;
var APPMarkerBegin = 0xffe0;
var APPMarkerEnd = 0xffef;
var data = void 0;
/**
 * @param buffer {Buffer}
 * @returns {Boolean}
 * @example
 * var content = fs.readFileSync("~/Picture/IMG_0911.JPG");
 * var isImage = isValid(content);
 * console.log(isImage);
 */
var isValid = function isValid(buffer) {
  try {
    var SOIMarker = buffer.readUInt16BE(0);
    return SOIMarker === JPEGSOIMarker;
  } catch (e) {
    throw new Error('Unsupport file format.');
  }
};
/**
 * @param buffer {Buffer}
 * @returns {Boolean}
 * @example
 */
var isTiff = function isTiff(buffer) {
  try {
    var SOIMarker = buffer.readUInt16BE(0);
    return SOIMarker === TIFFINTEL || SOIMarker === TIFFMOTOROLA;
  } catch (e) {
    throw new Error('Unsupport file format.');
  }
};
/**
 * @param buffer {Buffer}
 * @returns {Number}
 * @example
 * var content = fs.readFileSync("~/Picture/IMG_0911.JPG");
 * var APPNumber = checkAPPn(content);
 * console.log(APPNumber);
 */
var checkAPPn = function checkAPPn(buffer) {
  try {
    var APPMarkerTag = buffer.readUInt16BE(0);
    var isInRange = APPMarkerTag >= APPMarkerBegin && APPMarkerTag <= APPMarkerEnd;
    return isInRange ? APPMarkerTag - APPMarkerBegin : false;
  } catch (e) {
    throw new Error('Invalid APP Tag.');
  }
};
/**
 * @param buffer {Buffer}
 * @param tagCollection {Object}
 * @param order {Boolean}
 * @param offset {Number}
 * @returns {Object}
 * @example
 * var content = fs.readFileSync("~/Picture/IMG_0911.JPG");
 * var exifFragments = IFDHandler(content, 0, true, 8);
 * console.log(exifFragments.value);
 */
var IFDHandler = function IFDHandler(buffer, tagCollection, order, offset) {
  var entriesNumber = order ? buffer.readUInt16BE(0) : buffer.readUInt16LE(0);

  if (entriesNumber === 0) {
    return {};
  }

  var entriesNumberLength = 2;
  var entries = buffer.slice(entriesNumberLength);
  var entryLength = 12;
  // let nextIFDPointerBegin = entriesNumberLength + entryLength * entriesNumber;
  // let bigNextIFDPointer= buffer.readUInt32BE(nextIFDPointerBegin) ;
  // let littleNextIFDPointer= buffer.readUInt32LE(nextIFDPointerBegin);
  // let nextIFDPointer = order ?bigNextIFDPointer:littleNextIFDPointer;
  var exif = {};
  var entryCount = 0;

  for (entryCount; entryCount < entriesNumber; entryCount += 1) {
    var entryBegin = entryCount * entryLength;
    var entry = entries.slice(entryBegin, entryBegin + entryLength);
    var tagBegin = 0;
    var tagLength = 2;
    var dataFormatBegin = tagBegin + tagLength;
    var dataFormatLength = 2;
    var componentsBegin = dataFormatBegin + dataFormatLength;
    var componentsNumberLength = 4;
    var dataValueBegin = componentsBegin + componentsNumberLength;
    var dataValueLength = 4;
    var tagAddress = entry.slice(tagBegin, dataFormatBegin);
    var tagNumber = order ? tagAddress.toString('hex') : tagAddress.reverse().toString('hex');
    var tagName = tagCollection[tagNumber];
    var bigDataFormat = entry.readUInt16BE(dataFormatBegin);
    var littleDataFormat = entry.readUInt16LE(dataFormatBegin);
    var dataFormat = order ? bigDataFormat : littleDataFormat;
    var componentsByte = bytes[dataFormat];
    var bigComponentsNumber = entry.readUInt32BE(componentsBegin);
    var littleComponentNumber = entry.readUInt32LE(componentsBegin);
    var componentsNumber = order ? bigComponentsNumber : littleComponentNumber;
    var dataLength = componentsNumber * componentsByte;
    var dataValue = entry.slice(dataValueBegin, dataValueBegin + dataValueLength);

    if (dataLength > 4) {
      var dataOffset = (order ? dataValue.readUInt32BE(0) : dataValue.readUInt32LE(0)) - offset;
      dataValue = buffer.slice(dataOffset, dataOffset + dataLength);
    }

    var tagValue = void 0;

    if (tagName) {
      switch (dataFormat) {
        case 1:
          tagValue = dataValue.readUInt8(0);
          break;
        case 2:
          tagValue = dataValue.toString('ascii').replace(/\0+$/, '');
          break;
        case 3:
          tagValue = order ? dataValue.readUInt16BE(0) : dataValue.readUInt16LE(0);
          break;
        case 4:
          tagValue = order ? dataValue.readUInt32BE(0) : dataValue.readUInt32LE(0);
          break;
        case 5:
          tagValue = [];

          for (var i = 0; i < dataValue.length; i += 8) {
            var bigTagValue = dataValue.readUInt32BE(i) / dataValue.readUInt32BE(i + 4);
            var littleTagValue = dataValue.readUInt32LE(i) / dataValue.readUInt32LE(i + 4);
            tagValue.push(order ? bigTagValue : littleTagValue);
          }

          break;
        case 7:
          switch (tagName) {
            case 'ExifVersion':
              tagValue = dataValue.toString();
              break;
            case 'FlashPixVersion':
              tagValue = dataValue.toString();
              break;
            case 'SceneType':
              tagValue = dataValue.readUInt8(0);
              break;
            default:
              tagValue = '0x' + dataValue.toString('hex', 0, 15);
              break;
          }
          break;
        case 10:
          {
            var bigOrder = dataValue.readInt32BE(0) / dataValue.readInt32BE(4);
            var littleOrder = dataValue.readInt32LE(0) / dataValue.readInt32LE(4);
            tagValue = order ? bigOrder : littleOrder;
            break;
          }
        default:
          tagValue = '0x' + dataValue.toString('hex');
          break;
      }
      exif[tagName] = tagValue;
    }
    /*
     else {
     console.log(`Unkown Tag [0x${tagNumber}].`);
     }
     */
  }
  return exif;
};

/**
 * @param buf {Buffer}
 * @returns {Undefined}
 * @example
 * var content = fs.readFileSync("~/Picture/IMG_0911.JPG");
 * var exifFragments = EXIFHandler(content);
 */
var EXIFHandler = function EXIFHandler(buf) {
  var pad = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

  var buffer = buf;

  if (pad) {
    buffer = buf.slice(APPMarkerLength);
    var length = buffer.readUInt16BE(0);
    buffer = buffer.slice(0, length);
    var lengthLength = 2;
    buffer = buffer.slice(lengthLength);
    var identifierLength = 5;
    buffer = buffer.slice(identifierLength);
    var padLength = 1;
    buffer = buffer.slice(padLength);
  }

  var byteOrderLength = 2;
  var byteOrder = buffer.toString('ascii', 0, byteOrderLength) === 'MM';
  var fortyTwoLength = 2;
  var fortyTwoEnd = byteOrderLength + fortyTwoLength;
  var big42 = buffer.readUInt32BE(fortyTwoEnd);
  var little42 = buffer.readUInt32LE(fortyTwoEnd);
  var offsetOfIFD = byteOrder ? big42 : little42;

  buffer = buffer.slice(offsetOfIFD);

  if (buffer.length > 0) {
    data = IFDHandler(buffer, tags.ifd, byteOrder, offsetOfIFD);

    if (data.ExifIFDPointer) {
      buffer = buffer.slice(data.ExifIFDPointer - offsetOfIFD);
      data.SubExif = IFDHandler(buffer, tags.ifd, byteOrder, data.ExifIFDPointer);
    }

    if (data.GPSInfoIFDPointer) {
      var gps = data.GPSInfoIFDPointer;
      buffer = buffer.slice(data.ExifIFDPointer ? gps - data.ExifIFDPointer : gps - offsetOfIFD);
      data.GPSInfo = IFDHandler(buffer, tags.gps, byteOrder, gps);
    }
  }
};

/**
 * @param buffer {Buffer}
 * @returns {Undefined}
 * @example
 * var content = fs.readFileSync("~/Picture/IMG_0911.JPG");
 * var exifFragments = APPnHandler(content);
 */
var APPnHandler = function APPnHandler(buffer) {
  var APPMarkerTag = checkAPPn(buffer);

  if (APPMarkerTag !== false) {
    // APP0 is 0, and 0==false
    var length = buffer.readUInt16BE(APPMarkerLength);

    switch (APPMarkerTag) {
      case 1:
        // EXIF
        EXIFHandler(buffer);
        break;
      default:
        APPnHandler(buffer.slice(APPMarkerLength + length));
        break;
    }
  }
};

/**
 * @param buffer {Buffer}
 * @returns {Object}
 * @example
 */
var fromBuffer = function fromBuffer(buffer) {
  if (!buffer) {
    throw new Error('buffer not found');
  }

  data = undefined;

  if (isValid(buffer)) {
    buffer = buffer.slice(SOIMarkerLength);
    data = {};
    APPnHandler(buffer);
  } else if (isTiff(buffer)) {
    data = {};
    EXIFHandler(buffer, false);
  }

  return data;
};

/**
 * @param file {String}
 * @returns {Object}
 * @example
 * var exif = sync("~/Picture/IMG_1981.JPG");
 * console.log(exif.createTime);
 */
var sync = function sync(file) {
  if (!file) {
    throw new Error('File not found');
  }

  var buffer = _fs2.default.readFileSync(file);

  return fromBuffer(buffer);
};

/**
 * @param file {String}
 * @param callback {Function}
 * @example
 * async("~/Picture/IMG_0707.JPG", (err, data) => {
 *     if(err) {
 *         console.log(err);
 *     }
 *     if(data) {
 *         console.log(data.ExifOffset.createTime);
 *     }
 * }
 */
var async = function async(file, callback) {
  data = undefined;

  new Promise(function (resolve, reject) {
    if (!file) {
      reject(new Error('❓File not found.'));
    }

    _fs2.default.readFile(file, function (err, buffer) {
      if (err) {
        reject(err);
      } else {
        try {
          if (isValid(buffer)) {
            var buf = buffer.slice(SOIMarkerLength);

            data = {};

            APPnHandler(buf);
            resolve(data);
          } else if (isTiff(buffer)) {
            data = {};

            EXIFHandler(buffer, false);
            resolve(data);
          } else {
            reject(new Error('😱Unsupport file type.'));
          }
        } catch (e) {
          reject(e);
        }
      }
    });
  }, function (error) {
    callback(error, undefined);
  }).then(function (d) {
    callback(undefined, d);
  }).catch(function (error) {
    callback(error, undefined);
  });
};

exports.fromBuffer = fromBuffer;
exports.parse = async;
exports.parseSync = sync;


/***/ }),

/***/ 50716:
/***/ (function(module) {

"use strict";


/** @type {import('./abs')} */
module.exports = Math.abs;


/***/ }),

/***/ 77450:
/***/ (function(module) {

"use strict";


/** @type {import('./floor')} */
module.exports = Math.floor;


/***/ }),

/***/ 3774:
/***/ (function(module) {

"use strict";


/** @type {import('./max')} */
module.exports = Math.max;


/***/ }),

/***/ 47552:
/***/ (function(module) {

"use strict";


/** @type {import('./min')} */
module.exports = Math.min;


/***/ }),

/***/ 75874:
/***/ (function(module) {

"use strict";


/** @type {import('./pow')} */
module.exports = Math.pow;


/***/ }),

/***/ 63249:
/***/ (function(module) {

"use strict";


var numberIsNaN = function (value) {
	return value !== value;
};

module.exports = function is(a, b) {
	if (a === 0 && b === 0) {
		return 1 / a === 1 / b;
	}
	if (a === b) {
		return true;
	}
	if (numberIsNaN(a) && numberIsNaN(b)) {
		return true;
	}
	return false;
};



/***/ }),

/***/ 98527:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(77802);
var callBind = __webpack_require__(26601);

var implementation = __webpack_require__(63249);
var getPolyfill = __webpack_require__(89636);
var shim = __webpack_require__(3534);

var polyfill = callBind(getPolyfill(), Object);

define(polyfill, {
	getPolyfill: getPolyfill,
	implementation: implementation,
	shim: shim
});

module.exports = polyfill;


/***/ }),

/***/ 89636:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(63249);

module.exports = function getPolyfill() {
	return typeof Object.is === 'function' ? Object.is : implementation;
};


/***/ }),

/***/ 3534:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var getPolyfill = __webpack_require__(89636);
var define = __webpack_require__(77802);

module.exports = function shimObjectIs() {
	var polyfill = getPolyfill();
	define(Object, { is: polyfill }, {
		is: function testObjectIs() {
			return Object.is !== polyfill;
		}
	});
	return polyfill;
};


/***/ }),

/***/ 48461:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var keysShim;
if (!Object.keys) {
	// modified from https://github.com/es-shims/es5-shim
	var has = Object.prototype.hasOwnProperty;
	var toStr = Object.prototype.toString;
	var isArgs = __webpack_require__(76515); // eslint-disable-line global-require
	var isEnumerable = Object.prototype.propertyIsEnumerable;
	var hasDontEnumBug = !isEnumerable.call({ toString: null }, 'toString');
	var hasProtoEnumBug = isEnumerable.call(function () {}, 'prototype');
	var dontEnums = [
		'toString',
		'toLocaleString',
		'valueOf',
		'hasOwnProperty',
		'isPrototypeOf',
		'propertyIsEnumerable',
		'constructor'
	];
	var equalsConstructorPrototype = function (o) {
		var ctor = o.constructor;
		return ctor && ctor.prototype === o;
	};
	var excludedKeys = {
		$applicationCache: true,
		$console: true,
		$external: true,
		$frame: true,
		$frameElement: true,
		$frames: true,
		$innerHeight: true,
		$innerWidth: true,
		$onmozfullscreenchange: true,
		$onmozfullscreenerror: true,
		$outerHeight: true,
		$outerWidth: true,
		$pageXOffset: true,
		$pageYOffset: true,
		$parent: true,
		$scrollLeft: true,
		$scrollTop: true,
		$scrollX: true,
		$scrollY: true,
		$self: true,
		$webkitIndexedDB: true,
		$webkitStorageInfo: true,
		$window: true
	};
	var hasAutomationEqualityBug = (function () {
		/* global window */
		if (typeof window === 'undefined') { return false; }
		for (var k in window) {
			try {
				if (!excludedKeys['$' + k] && has.call(window, k) && window[k] !== null && typeof window[k] === 'object') {
					try {
						equalsConstructorPrototype(window[k]);
					} catch (e) {
						return true;
					}
				}
			} catch (e) {
				return true;
			}
		}
		return false;
	}());
	var equalsConstructorPrototypeIfNotBuggy = function (o) {
		/* global window */
		if (typeof window === 'undefined' || !hasAutomationEqualityBug) {
			return equalsConstructorPrototype(o);
		}
		try {
			return equalsConstructorPrototype(o);
		} catch (e) {
			return false;
		}
	};

	keysShim = function keys(object) {
		var isObject = object !== null && typeof object === 'object';
		var isFunction = toStr.call(object) === '[object Function]';
		var isArguments = isArgs(object);
		var isString = isObject && toStr.call(object) === '[object String]';
		var theKeys = [];

		if (!isObject && !isFunction && !isArguments) {
			throw new TypeError('Object.keys called on a non-object');
		}

		var skipProto = hasProtoEnumBug && isFunction;
		if (isString && object.length > 0 && !has.call(object, 0)) {
			for (var i = 0; i < object.length; ++i) {
				theKeys.push(String(i));
			}
		}

		if (isArguments && object.length > 0) {
			for (var j = 0; j < object.length; ++j) {
				theKeys.push(String(j));
			}
		} else {
			for (var name in object) {
				if (!(skipProto && name === 'prototype') && has.call(object, name)) {
					theKeys.push(String(name));
				}
			}
		}

		if (hasDontEnumBug) {
			var skipConstructor = equalsConstructorPrototypeIfNotBuggy(object);

			for (var k = 0; k < dontEnums.length; ++k) {
				if (!(skipConstructor && dontEnums[k] === 'constructor') && has.call(object, dontEnums[k])) {
					theKeys.push(dontEnums[k]);
				}
			}
		}
		return theKeys;
	};
}
module.exports = keysShim;


/***/ }),

/***/ 35643:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var slice = Array.prototype.slice;
var isArgs = __webpack_require__(76515);

var origKeys = Object.keys;
var keysShim = origKeys ? function keys(o) { return origKeys(o); } : __webpack_require__(48461);

var originalKeys = Object.keys;

keysShim.shim = function shimObjectKeys() {
	if (Object.keys) {
		var keysWorksWithArguments = (function () {
			// Safari 5.0 bug
			var args = Object.keys(arguments);
			return args && args.length === arguments.length;
		}(1, 2));
		if (!keysWorksWithArguments) {
			Object.keys = function keys(object) { // eslint-disable-line func-name-matching
				if (isArgs(object)) {
					return originalKeys(slice.call(object));
				}
				return originalKeys(object);
			};
		}
	} else {
		Object.keys = keysShim;
	}
	return Object.keys || keysShim;
};

module.exports = keysShim;


/***/ }),

/***/ 76515:
/***/ (function(module) {

"use strict";


var toStr = Object.prototype.toString;

module.exports = function isArguments(value) {
	var str = toStr.call(value);
	var isArgs = str === '[object Arguments]';
	if (!isArgs) {
		isArgs = str !== '[object Array]' &&
			value !== null &&
			typeof value === 'object' &&
			typeof value.length === 'number' &&
			value.length >= 0 &&
			toStr.call(value.callee) === '[object Function]';
	}
	return isArgs;
};


/***/ }),

/***/ 36521:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


// modified from https://github.com/es-shims/es6-shim
var objectKeys = __webpack_require__(35643);
var hasSymbols = __webpack_require__(12843)();
var callBound = __webpack_require__(22774);
var $Object = __webpack_require__(75846);
var $push = callBound('Array.prototype.push');
var $propIsEnumerable = callBound('Object.prototype.propertyIsEnumerable');
var originalGetSymbols = hasSymbols ? $Object.getOwnPropertySymbols : null;

// eslint-disable-next-line no-unused-vars
module.exports = function assign(target, source1) {
	if (target == null) { throw new TypeError('target must be an object'); }
	var to = $Object(target); // step 1
	if (arguments.length === 1) {
		return to; // step 2
	}
	for (var s = 1; s < arguments.length; ++s) {
		var from = $Object(arguments[s]); // step 3.a.i

		// step 3.a.ii:
		var keys = objectKeys(from);
		var getSymbols = hasSymbols && ($Object.getOwnPropertySymbols || originalGetSymbols);
		if (getSymbols) {
			var syms = getSymbols(from);
			for (var j = 0; j < syms.length; ++j) {
				var key = syms[j];
				if ($propIsEnumerable(from, key)) {
					$push(keys, key);
				}
			}
		}

		// step 3.a.iii:
		for (var i = 0; i < keys.length; ++i) {
			var nextKey = keys[i];
			if ($propIsEnumerable(from, nextKey)) { // step 3.a.iii.2
				var propValue = from[nextKey]; // step 3.a.iii.2.a
				to[nextKey] = propValue; // step 3.a.iii.2.b
			}
		}
	}

	return to; // step 4
};


/***/ }),

/***/ 37596:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(36521);

var lacksProperEnumerationOrder = function () {
	if (!Object.assign) {
		return false;
	}
	/*
	 * v8, specifically in node 4.x, has a bug with incorrect property enumeration order
	 * note: this does not detect the bug unless there's 20 characters
	 */
	var str = 'abcdefghijklmnopqrst';
	var letters = str.split('');
	var map = {};
	for (var i = 0; i < letters.length; ++i) {
		map[letters[i]] = letters[i];
	}
	var obj = Object.assign({}, map);
	var actual = '';
	for (var k in obj) {
		actual += k;
	}
	return str !== actual;
};

var assignHasPendingExceptions = function () {
	if (!Object.assign || !Object.preventExtensions) {
		return false;
	}
	/*
	 * Firefox 37 still has "pending exception" logic in its Object.assign implementation,
	 * which is 72% slower than our shim, and Firefox 40's native implementation.
	 */
	var thrower = Object.preventExtensions({ 1: 2 });
	try {
		Object.assign(thrower, 'xy');
	} catch (e) {
		return thrower[1] === 'y';
	}
	return false;
};

module.exports = function getPolyfill() {
	if (!Object.assign) {
		return implementation;
	}
	if (lacksProperEnumerationOrder()) {
		return implementation;
	}
	if (assignHasPendingExceptions()) {
		return implementation;
	}
	return Object.assign;
};


/***/ }),

/***/ 72519:
/***/ (function(__unused_webpack_module, exports) {

"use strict";



var TYPED_OK =  (typeof Uint8Array !== 'undefined') &&
                (typeof Uint16Array !== 'undefined') &&
                (typeof Int32Array !== 'undefined');

function _has(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

exports.assign = function (obj /*from1, from2, from3, ...*/) {
  var sources = Array.prototype.slice.call(arguments, 1);
  while (sources.length) {
    var source = sources.shift();
    if (!source) { continue; }

    if (typeof source !== 'object') {
      throw new TypeError(source + 'must be non-object');
    }

    for (var p in source) {
      if (_has(source, p)) {
        obj[p] = source[p];
      }
    }
  }

  return obj;
};


// reduce buffer size, avoiding mem copy
exports.shrinkBuf = function (buf, size) {
  if (buf.length === size) { return buf; }
  if (buf.subarray) { return buf.subarray(0, size); }
  buf.length = size;
  return buf;
};


var fnTyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    if (src.subarray && dest.subarray) {
      dest.set(src.subarray(src_offs, src_offs + len), dest_offs);
      return;
    }
    // Fallback to ordinary array
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    var i, l, len, pos, chunk, result;

    // calculate data length
    len = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      len += chunks[i].length;
    }

    // join chunks
    result = new Uint8Array(len);
    pos = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      chunk = chunks[i];
      result.set(chunk, pos);
      pos += chunk.length;
    }

    return result;
  }
};

var fnUntyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    return [].concat.apply([], chunks);
  }
};


// Enable/Disable typed arrays use, for testing
//
exports.setTyped = function (on) {
  if (on) {
    exports.Buf8  = Uint8Array;
    exports.Buf16 = Uint16Array;
    exports.Buf32 = Int32Array;
    exports.assign(exports, fnTyped);
  } else {
    exports.Buf8  = Array;
    exports.Buf16 = Array;
    exports.Buf32 = Array;
    exports.assign(exports, fnUntyped);
  }
};

exports.setTyped(TYPED_OK);


/***/ }),

/***/ 46911:
/***/ (function(module) {

"use strict";


// Note: adler32 takes 12% for level 0 and 2% for level 6.
// It isn't worth it to make additional optimizations as in original.
// Small size is preferable.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function adler32(adler, buf, len, pos) {
  var s1 = (adler & 0xffff) |0,
      s2 = ((adler >>> 16) & 0xffff) |0,
      n = 0;

  while (len !== 0) {
    // Set limit ~ twice less than 5552, to keep
    // s2 in 31-bits, because we force signed ints.
    // in other case %= will fail.
    n = len > 2000 ? 2000 : len;
    len -= n;

    do {
      s1 = (s1 + buf[pos++]) |0;
      s2 = (s2 + s1) |0;
    } while (--n);

    s1 %= 65521;
    s2 %= 65521;
  }

  return (s1 | (s2 << 16)) |0;
}


module.exports = adler32;


/***/ }),

/***/ 81607:
/***/ (function(module) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {

  /* Allowed flush values; see deflate() and inflate() below for details */
  Z_NO_FLUSH:         0,
  Z_PARTIAL_FLUSH:    1,
  Z_SYNC_FLUSH:       2,
  Z_FULL_FLUSH:       3,
  Z_FINISH:           4,
  Z_BLOCK:            5,
  Z_TREES:            6,

  /* Return codes for the compression/decompression functions. Negative values
  * are errors, positive values are used for special but normal events.
  */
  Z_OK:               0,
  Z_STREAM_END:       1,
  Z_NEED_DICT:        2,
  Z_ERRNO:           -1,
  Z_STREAM_ERROR:    -2,
  Z_DATA_ERROR:      -3,
  //Z_MEM_ERROR:     -4,
  Z_BUF_ERROR:       -5,
  //Z_VERSION_ERROR: -6,

  /* compression levels */
  Z_NO_COMPRESSION:         0,
  Z_BEST_SPEED:             1,
  Z_BEST_COMPRESSION:       9,
  Z_DEFAULT_COMPRESSION:   -1,


  Z_FILTERED:               1,
  Z_HUFFMAN_ONLY:           2,
  Z_RLE:                    3,
  Z_FIXED:                  4,
  Z_DEFAULT_STRATEGY:       0,

  /* Possible values of the data_type field (though see inflate()) */
  Z_BINARY:                 0,
  Z_TEXT:                   1,
  //Z_ASCII:                1, // = Z_TEXT (deprecated)
  Z_UNKNOWN:                2,

  /* The deflate compression method */
  Z_DEFLATED:               8
  //Z_NULL:                 null // Use -1 or null inline, depending on var type
};


/***/ }),

/***/ 99049:
/***/ (function(module) {

"use strict";


// Note: we can't get significant speed boost here.
// So write code to minimize size - no pregenerated tables
// and array tools dependencies.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// Use ordinary array, since untyped makes no boost here
function makeTable() {
  var c, table = [];

  for (var n = 0; n < 256; n++) {
    c = n;
    for (var k = 0; k < 8; k++) {
      c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
    }
    table[n] = c;
  }

  return table;
}

// Create table on load. Just 255 signed longs. Not a problem.
var crcTable = makeTable();


function crc32(crc, buf, len, pos) {
  var t = crcTable,
      end = pos + len;

  crc ^= -1;

  for (var i = pos; i < end; i++) {
    crc = (crc >>> 8) ^ t[(crc ^ buf[i]) & 0xFF];
  }

  return (crc ^ (-1)); // >>> 0;
}


module.exports = crc32;


/***/ }),

/***/ 22925:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils   = __webpack_require__(72519);
var trees   = __webpack_require__(22367);
var adler32 = __webpack_require__(46911);
var crc32   = __webpack_require__(99049);
var msg     = __webpack_require__(56228);

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
var Z_NO_FLUSH      = 0;
var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
//var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
//var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
//var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;


/* compression levels */
//var Z_NO_COMPRESSION      = 0;
//var Z_BEST_SPEED          = 1;
//var Z_BEST_COMPRESSION    = 9;
var Z_DEFAULT_COMPRESSION = -1;


var Z_FILTERED            = 1;
var Z_HUFFMAN_ONLY        = 2;
var Z_RLE                 = 3;
var Z_FIXED               = 4;
var Z_DEFAULT_STRATEGY    = 0;

/* Possible values of the data_type field (though see inflate()) */
//var Z_BINARY              = 0;
//var Z_TEXT                = 1;
//var Z_ASCII               = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;


/* The deflate compression method */
var Z_DEFLATED  = 8;

/*============================================================================*/


var MAX_MEM_LEVEL = 9;
/* Maximum value for memLevel in deflateInit2 */
var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_MEM_LEVEL = 8;


var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */
var LITERALS      = 256;
/* number of literal bytes 0..255 */
var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */
var D_CODES       = 30;
/* number of distance codes */
var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */
var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */
var MAX_BITS  = 15;
/* All codes must not exceed MAX_BITS bits */

var MIN_MATCH = 3;
var MAX_MATCH = 258;
var MIN_LOOKAHEAD = (MAX_MATCH + MIN_MATCH + 1);

var PRESET_DICT = 0x20;

var INIT_STATE = 42;
var EXTRA_STATE = 69;
var NAME_STATE = 73;
var COMMENT_STATE = 91;
var HCRC_STATE = 103;
var BUSY_STATE = 113;
var FINISH_STATE = 666;

var BS_NEED_MORE      = 1; /* block not completed, need more input or more output */
var BS_BLOCK_DONE     = 2; /* block flush performed */
var BS_FINISH_STARTED = 3; /* finish started, need only more output at next deflate */
var BS_FINISH_DONE    = 4; /* finish done, accept no more input or output */

var OS_CODE = 0x03; // Unix :) . Don't detect, use this default.

function err(strm, errorCode) {
  strm.msg = msg[errorCode];
  return errorCode;
}

function rank(f) {
  return ((f) << 1) - ((f) > 4 ? 9 : 0);
}

function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }


/* =========================================================================
 * Flush as much pending output as possible. All deflate() output goes
 * through this function so some applications may wish to modify it
 * to avoid allocating a large strm->output buffer and copying into it.
 * (See also read_buf()).
 */
function flush_pending(strm) {
  var s = strm.state;

  //_tr_flush_bits(s);
  var len = s.pending;
  if (len > strm.avail_out) {
    len = strm.avail_out;
  }
  if (len === 0) { return; }

  utils.arraySet(strm.output, s.pending_buf, s.pending_out, len, strm.next_out);
  strm.next_out += len;
  s.pending_out += len;
  strm.total_out += len;
  strm.avail_out -= len;
  s.pending -= len;
  if (s.pending === 0) {
    s.pending_out = 0;
  }
}


function flush_block_only(s, last) {
  trees._tr_flush_block(s, (s.block_start >= 0 ? s.block_start : -1), s.strstart - s.block_start, last);
  s.block_start = s.strstart;
  flush_pending(s.strm);
}


function put_byte(s, b) {
  s.pending_buf[s.pending++] = b;
}


/* =========================================================================
 * Put a short in the pending buffer. The 16-bit value is put in MSB order.
 * IN assertion: the stream state is correct and there is enough room in
 * pending_buf.
 */
function putShortMSB(s, b) {
//  put_byte(s, (Byte)(b >> 8));
//  put_byte(s, (Byte)(b & 0xff));
  s.pending_buf[s.pending++] = (b >>> 8) & 0xff;
  s.pending_buf[s.pending++] = b & 0xff;
}


/* ===========================================================================
 * Read a new buffer from the current input stream, update the adler32
 * and total number of bytes read.  All deflate() input goes through
 * this function so some applications may wish to modify it to avoid
 * allocating a large strm->input buffer and copying from it.
 * (See also flush_pending()).
 */
function read_buf(strm, buf, start, size) {
  var len = strm.avail_in;

  if (len > size) { len = size; }
  if (len === 0) { return 0; }

  strm.avail_in -= len;

  // zmemcpy(buf, strm->next_in, len);
  utils.arraySet(buf, strm.input, strm.next_in, len, start);
  if (strm.state.wrap === 1) {
    strm.adler = adler32(strm.adler, buf, len, start);
  }

  else if (strm.state.wrap === 2) {
    strm.adler = crc32(strm.adler, buf, len, start);
  }

  strm.next_in += len;
  strm.total_in += len;

  return len;
}


/* ===========================================================================
 * Set match_start to the longest match starting at the given string and
 * return its length. Matches shorter or equal to prev_length are discarded,
 * in which case the result is equal to prev_length and match_start is
 * garbage.
 * IN assertions: cur_match is the head of the hash chain for the current
 *   string (strstart) and its distance is <= MAX_DIST, and prev_length >= 1
 * OUT assertion: the match length is not greater than s->lookahead.
 */
function longest_match(s, cur_match) {
  var chain_length = s.max_chain_length;      /* max hash chain length */
  var scan = s.strstart; /* current string */
  var match;                       /* matched string */
  var len;                           /* length of current match */
  var best_len = s.prev_length;              /* best match length so far */
  var nice_match = s.nice_match;             /* stop if match long enough */
  var limit = (s.strstart > (s.w_size - MIN_LOOKAHEAD)) ?
      s.strstart - (s.w_size - MIN_LOOKAHEAD) : 0/*NIL*/;

  var _win = s.window; // shortcut

  var wmask = s.w_mask;
  var prev  = s.prev;

  /* Stop when cur_match becomes <= limit. To simplify the code,
   * we prevent matches with the string of window index 0.
   */

  var strend = s.strstart + MAX_MATCH;
  var scan_end1  = _win[scan + best_len - 1];
  var scan_end   = _win[scan + best_len];

  /* The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of 16.
   * It is easy to get rid of this optimization if necessary.
   */
  // Assert(s->hash_bits >= 8 && MAX_MATCH == 258, "Code too clever");

  /* Do not waste too much time if we already have a good match: */
  if (s.prev_length >= s.good_match) {
    chain_length >>= 2;
  }
  /* Do not look for matches beyond the end of the input. This is necessary
   * to make deflate deterministic.
   */
  if (nice_match > s.lookahead) { nice_match = s.lookahead; }

  // Assert((ulg)s->strstart <= s->window_size-MIN_LOOKAHEAD, "need lookahead");

  do {
    // Assert(cur_match < s->strstart, "no future");
    match = cur_match;

    /* Skip to next match if the match length cannot increase
     * or if the match length is less than 2.  Note that the checks below
     * for insufficient lookahead only occur occasionally for performance
     * reasons.  Therefore uninitialized memory will be accessed, and
     * conditional jumps will be made that depend on those values.
     * However the length of the match is limited to the lookahead, so
     * the output of deflate is not affected by the uninitialized values.
     */

    if (_win[match + best_len]     !== scan_end  ||
        _win[match + best_len - 1] !== scan_end1 ||
        _win[match]                !== _win[scan] ||
        _win[++match]              !== _win[scan + 1]) {
      continue;
    }

    /* The check at best_len-1 can be removed because it will be made
     * again later. (This heuristic is not always a win.)
     * It is not necessary to compare scan[2] and match[2] since they
     * are always equal when the other bytes match, given that
     * the hash keys are equal and that HASH_BITS >= 8.
     */
    scan += 2;
    match++;
    // Assert(*scan == *match, "match[2]?");

    /* We check for insufficient lookahead only every 8th comparison;
     * the 256th check will be made at strstart+258.
     */
    do {
      /*jshint noempty:false*/
    } while (_win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             scan < strend);

    // Assert(scan <= s->window+(unsigned)(s->window_size-1), "wild scan");

    len = MAX_MATCH - (strend - scan);
    scan = strend - MAX_MATCH;

    if (len > best_len) {
      s.match_start = cur_match;
      best_len = len;
      if (len >= nice_match) {
        break;
      }
      scan_end1  = _win[scan + best_len - 1];
      scan_end   = _win[scan + best_len];
    }
  } while ((cur_match = prev[cur_match & wmask]) > limit && --chain_length !== 0);

  if (best_len <= s.lookahead) {
    return best_len;
  }
  return s.lookahead;
}


/* ===========================================================================
 * Fill the window when the lookahead becomes insufficient.
 * Updates strstart and lookahead.
 *
 * IN assertion: lookahead < MIN_LOOKAHEAD
 * OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
 *    At least one byte has been read, or avail_in == 0; reads are
 *    performed for at least two bytes (required for the zip translate_eol
 *    option -- not supported here).
 */
function fill_window(s) {
  var _w_size = s.w_size;
  var p, n, m, more, str;

  //Assert(s->lookahead < MIN_LOOKAHEAD, "already enough lookahead");

  do {
    more = s.window_size - s.lookahead - s.strstart;

    // JS ints have 32 bit, block below not needed
    /* Deal with !@#$% 64K limit: */
    //if (sizeof(int) <= 2) {
    //    if (more == 0 && s->strstart == 0 && s->lookahead == 0) {
    //        more = wsize;
    //
    //  } else if (more == (unsigned)(-1)) {
    //        /* Very unlikely, but possible on 16 bit machine if
    //         * strstart == 0 && lookahead == 1 (input done a byte at time)
    //         */
    //        more--;
    //    }
    //}


    /* If the window is almost full and there is insufficient lookahead,
     * move the upper half to the lower one to make room in the upper half.
     */
    if (s.strstart >= _w_size + (_w_size - MIN_LOOKAHEAD)) {

      utils.arraySet(s.window, s.window, _w_size, _w_size, 0);
      s.match_start -= _w_size;
      s.strstart -= _w_size;
      /* we now have strstart >= MAX_DIST */
      s.block_start -= _w_size;

      /* Slide the hash table (could be avoided with 32 bit values
       at the expense of memory usage). We slide even when level == 0
       to keep the hash table consistent if we switch back to level > 0
       later. (Using level 0 permanently is not an optimal usage of
       zlib, so we don't care about this pathological case.)
       */

      n = s.hash_size;
      p = n;
      do {
        m = s.head[--p];
        s.head[p] = (m >= _w_size ? m - _w_size : 0);
      } while (--n);

      n = _w_size;
      p = n;
      do {
        m = s.prev[--p];
        s.prev[p] = (m >= _w_size ? m - _w_size : 0);
        /* If n is not on any hash chain, prev[n] is garbage but
         * its value will never be used.
         */
      } while (--n);

      more += _w_size;
    }
    if (s.strm.avail_in === 0) {
      break;
    }

    /* If there was no sliding:
     *    strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
     *    more == window_size - lookahead - strstart
     * => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
     * => more >= window_size - 2*WSIZE + 2
     * In the BIG_MEM or MMAP case (not yet supported),
     *   window_size == input_size + MIN_LOOKAHEAD  &&
     *   strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
     * Otherwise, window_size == 2*WSIZE so more >= 2.
     * If there was sliding, more >= WSIZE. So in all cases, more >= 2.
     */
    //Assert(more >= 2, "more < 2");
    n = read_buf(s.strm, s.window, s.strstart + s.lookahead, more);
    s.lookahead += n;

    /* Initialize the hash value now that we have some input: */
    if (s.lookahead + s.insert >= MIN_MATCH) {
      str = s.strstart - s.insert;
      s.ins_h = s.window[str];

      /* UPDATE_HASH(s, s->ins_h, s->window[str + 1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + 1]) & s.hash_mask;
//#if MIN_MATCH != 3
//        Call update_hash() MIN_MATCH-3 more times
//#endif
      while (s.insert) {
        /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

        s.prev[str & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = str;
        str++;
        s.insert--;
        if (s.lookahead + s.insert < MIN_MATCH) {
          break;
        }
      }
    }
    /* If the whole input has less than MIN_MATCH bytes, ins_h is garbage,
     * but this is not important since only literal bytes will be emitted.
     */

  } while (s.lookahead < MIN_LOOKAHEAD && s.strm.avail_in !== 0);

  /* If the WIN_INIT bytes after the end of the current data have never been
   * written, then zero those bytes in order to avoid memory check reports of
   * the use of uninitialized (or uninitialised as Julian writes) bytes by
   * the longest match routines.  Update the high water mark for the next
   * time through here.  WIN_INIT is set to MAX_MATCH since the longest match
   * routines allow scanning to strstart + MAX_MATCH, ignoring lookahead.
   */
//  if (s.high_water < s.window_size) {
//    var curr = s.strstart + s.lookahead;
//    var init = 0;
//
//    if (s.high_water < curr) {
//      /* Previous high water mark below current data -- zero WIN_INIT
//       * bytes or up to end of window, whichever is less.
//       */
//      init = s.window_size - curr;
//      if (init > WIN_INIT)
//        init = WIN_INIT;
//      zmemzero(s->window + curr, (unsigned)init);
//      s->high_water = curr + init;
//    }
//    else if (s->high_water < (ulg)curr + WIN_INIT) {
//      /* High water mark at or above current data, but below current data
//       * plus WIN_INIT -- zero out to current data plus WIN_INIT, or up
//       * to end of window, whichever is less.
//       */
//      init = (ulg)curr + WIN_INIT - s->high_water;
//      if (init > s->window_size - s->high_water)
//        init = s->window_size - s->high_water;
//      zmemzero(s->window + s->high_water, (unsigned)init);
//      s->high_water += init;
//    }
//  }
//
//  Assert((ulg)s->strstart <= s->window_size - MIN_LOOKAHEAD,
//    "not enough room for search");
}

/* ===========================================================================
 * Copy without compression as much as possible from the input stream, return
 * the current block state.
 * This function does not insert new strings in the dictionary since
 * uncompressible data is probably not useful. This function is used
 * only for the level=0 compression option.
 * NOTE: this function should be optimized to avoid extra copying from
 * window to pending_buf.
 */
function deflate_stored(s, flush) {
  /* Stored blocks are limited to 0xffff bytes, pending_buf is limited
   * to pending_buf_size, and each stored block has a 5 byte header:
   */
  var max_block_size = 0xffff;

  if (max_block_size > s.pending_buf_size - 5) {
    max_block_size = s.pending_buf_size - 5;
  }

  /* Copy as much as possible from input to output: */
  for (;;) {
    /* Fill the window as much as possible: */
    if (s.lookahead <= 1) {

      //Assert(s->strstart < s->w_size+MAX_DIST(s) ||
      //  s->block_start >= (long)s->w_size, "slide too late");
//      if (!(s.strstart < s.w_size + (s.w_size - MIN_LOOKAHEAD) ||
//        s.block_start >= s.w_size)) {
//        throw  new Error("slide too late");
//      }

      fill_window(s);
      if (s.lookahead === 0 && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }

      if (s.lookahead === 0) {
        break;
      }
      /* flush the current block */
    }
    //Assert(s->block_start >= 0L, "block gone");
//    if (s.block_start < 0) throw new Error("block gone");

    s.strstart += s.lookahead;
    s.lookahead = 0;

    /* Emit a stored block if pending_buf will be full: */
    var max_start = s.block_start + max_block_size;

    if (s.strstart === 0 || s.strstart >= max_start) {
      /* strstart == 0 is possible when wraparound on 16-bit machine */
      s.lookahead = s.strstart - max_start;
      s.strstart = max_start;
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/


    }
    /* Flush if we may have to slide, otherwise block_start may become
     * negative and the data will be gone:
     */
    if (s.strstart - s.block_start >= (s.w_size - MIN_LOOKAHEAD)) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }

  s.insert = 0;

  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }

  if (s.strstart > s.block_start) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_NEED_MORE;
}

/* ===========================================================================
 * Compress as much as possible from the input stream, return the current
 * block state.
 * This function does not perform lazy evaluation of matches and inserts
 * new strings in the dictionary only for unmatched strings or for short
 * matches. It is used only for the fast compression options.
 */
function deflate_fast(s, flush) {
  var hash_head;        /* head of the hash chain */
  var bflush;           /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) {
        break; /* flush the current block */
      }
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     * At this point we have always match_length < MIN_MATCH
     */
    if (hash_head !== 0/*NIL*/ && ((s.strstart - hash_head) <= (s.w_size - MIN_LOOKAHEAD))) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */
    }
    if (s.match_length >= MIN_MATCH) {
      // check_match(s, s.strstart, s.match_start, s.match_length); // for debug only

      /*** _tr_tally_dist(s, s.strstart - s.match_start,
                     s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, s.strstart - s.match_start, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;

      /* Insert new strings in the hash table only if the match length
       * is not too large. This saves time but degrades compression.
       */
      if (s.match_length <= s.max_lazy_match/*max_insert_length*/ && s.lookahead >= MIN_MATCH) {
        s.match_length--; /* string at strstart already in table */
        do {
          s.strstart++;
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
          /* strstart never exceeds WSIZE-MAX_MATCH, so there are
           * always MIN_MATCH bytes ahead.
           */
        } while (--s.match_length !== 0);
        s.strstart++;
      } else
      {
        s.strstart += s.match_length;
        s.match_length = 0;
        s.ins_h = s.window[s.strstart];
        /* UPDATE_HASH(s, s.ins_h, s.window[s.strstart+1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + 1]) & s.hash_mask;

//#if MIN_MATCH != 3
//                Call UPDATE_HASH() MIN_MATCH-3 more times
//#endif
        /* If lookahead < MIN_MATCH, ins_h is garbage, but it does not
         * matter since it will be recomputed at next deflate call.
         */
      }
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s.window[s.strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = ((s.strstart < (MIN_MATCH - 1)) ? s.strstart : MIN_MATCH - 1);
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * Same as above, but achieves better compression. We use a lazy
 * evaluation for matches: a match is finally adopted only if there is
 * no better match at the next window position.
 */
function deflate_slow(s, flush) {
  var hash_head;          /* head of hash chain */
  var bflush;              /* set if current block must be flushed */

  var max_insert;

  /* Process the input block. */
  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     */
    s.prev_length = s.match_length;
    s.prev_match = s.match_start;
    s.match_length = MIN_MATCH - 1;

    if (hash_head !== 0/*NIL*/ && s.prev_length < s.max_lazy_match &&
        s.strstart - hash_head <= (s.w_size - MIN_LOOKAHEAD)/*MAX_DIST(s)*/) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */

      if (s.match_length <= 5 &&
         (s.strategy === Z_FILTERED || (s.match_length === MIN_MATCH && s.strstart - s.match_start > 4096/*TOO_FAR*/))) {

        /* If prev_match is also MIN_MATCH, match_start is garbage
         * but we will ignore the current match anyway.
         */
        s.match_length = MIN_MATCH - 1;
      }
    }
    /* If there was a match at the previous step and the current
     * match is not better, output the previous match:
     */
    if (s.prev_length >= MIN_MATCH && s.match_length <= s.prev_length) {
      max_insert = s.strstart + s.lookahead - MIN_MATCH;
      /* Do not insert strings in hash table beyond this. */

      //check_match(s, s.strstart-1, s.prev_match, s.prev_length);

      /***_tr_tally_dist(s, s.strstart - 1 - s.prev_match,
                     s.prev_length - MIN_MATCH, bflush);***/
      bflush = trees._tr_tally(s, s.strstart - 1 - s.prev_match, s.prev_length - MIN_MATCH);
      /* Insert in hash table all strings up to the end of the match.
       * strstart-1 and strstart are already inserted. If there is not
       * enough lookahead, the last two strings are not inserted in
       * the hash table.
       */
      s.lookahead -= s.prev_length - 1;
      s.prev_length -= 2;
      do {
        if (++s.strstart <= max_insert) {
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
        }
      } while (--s.prev_length !== 0);
      s.match_available = 0;
      s.match_length = MIN_MATCH - 1;
      s.strstart++;

      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }

    } else if (s.match_available) {
      /* If there was no match at the previous position, output a
       * single literal. If there was a match but the current match
       * is longer, truncate the previous match to a single literal.
       */
      //Tracevv((stderr,"%c", s->window[s->strstart-1]));
      /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

      if (bflush) {
        /*** FLUSH_BLOCK_ONLY(s, 0) ***/
        flush_block_only(s, false);
        /***/
      }
      s.strstart++;
      s.lookahead--;
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
    } else {
      /* There is no previous match to compare with, wait for
       * the next step to decide.
       */
      s.match_available = 1;
      s.strstart++;
      s.lookahead--;
    }
  }
  //Assert (flush != Z_NO_FLUSH, "no flush?");
  if (s.match_available) {
    //Tracevv((stderr,"%c", s->window[s->strstart-1]));
    /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

    s.match_available = 0;
  }
  s.insert = s.strstart < MIN_MATCH - 1 ? s.strstart : MIN_MATCH - 1;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_BLOCK_DONE;
}


/* ===========================================================================
 * For Z_RLE, simply look for runs of bytes, generate matches only of distance
 * one.  Do not maintain a hash table.  (It will be regenerated if this run of
 * deflate switches away from Z_RLE.)
 */
function deflate_rle(s, flush) {
  var bflush;            /* set if current block must be flushed */
  var prev;              /* byte at distance one to match */
  var scan, strend;      /* scan goes up to strend for length of run */

  var _win = s.window;

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the longest run, plus one for the unrolled loop.
     */
    if (s.lookahead <= MAX_MATCH) {
      fill_window(s);
      if (s.lookahead <= MAX_MATCH && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* See how many times the previous byte repeats */
    s.match_length = 0;
    if (s.lookahead >= MIN_MATCH && s.strstart > 0) {
      scan = s.strstart - 1;
      prev = _win[scan];
      if (prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan]) {
        strend = s.strstart + MAX_MATCH;
        do {
          /*jshint noempty:false*/
        } while (prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 scan < strend);
        s.match_length = MAX_MATCH - (strend - scan);
        if (s.match_length > s.lookahead) {
          s.match_length = s.lookahead;
        }
      }
      //Assert(scan <= s->window+(uInt)(s->window_size-1), "wild scan");
    }

    /* Emit match if have run of MIN_MATCH or longer, else emit literal */
    if (s.match_length >= MIN_MATCH) {
      //check_match(s, s.strstart, s.strstart - 1, s.match_length);

      /*** _tr_tally_dist(s, 1, s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, 1, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;
      s.strstart += s.match_length;
      s.match_length = 0;
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s->window[s->strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * For Z_HUFFMAN_ONLY, do not look for matches.  Do not maintain a hash table.
 * (It will be regenerated if this run of deflate switches away from Huffman.)
 */
function deflate_huff(s, flush) {
  var bflush;             /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we have a literal to write. */
    if (s.lookahead === 0) {
      fill_window(s);
      if (s.lookahead === 0) {
        if (flush === Z_NO_FLUSH) {
          return BS_NEED_MORE;
        }
        break;      /* flush the current block */
      }
    }

    /* Output a literal byte */
    s.match_length = 0;
    //Tracevv((stderr,"%c", s->window[s->strstart]));
    /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart]);
    s.lookahead--;
    s.strstart++;
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* Values for max_lazy_match, good_match and max_chain_length, depending on
 * the desired pack level (0..9). The values given below have been tuned to
 * exclude worst case performance for pathological files. Better values may be
 * found for specific files.
 */
function Config(good_length, max_lazy, nice_length, max_chain, func) {
  this.good_length = good_length;
  this.max_lazy = max_lazy;
  this.nice_length = nice_length;
  this.max_chain = max_chain;
  this.func = func;
}

var configuration_table;

configuration_table = [
  /*      good lazy nice chain */
  new Config(0, 0, 0, 0, deflate_stored),          /* 0 store only */
  new Config(4, 4, 8, 4, deflate_fast),            /* 1 max speed, no lazy matches */
  new Config(4, 5, 16, 8, deflate_fast),           /* 2 */
  new Config(4, 6, 32, 32, deflate_fast),          /* 3 */

  new Config(4, 4, 16, 16, deflate_slow),          /* 4 lazy matches */
  new Config(8, 16, 32, 32, deflate_slow),         /* 5 */
  new Config(8, 16, 128, 128, deflate_slow),       /* 6 */
  new Config(8, 32, 128, 256, deflate_slow),       /* 7 */
  new Config(32, 128, 258, 1024, deflate_slow),    /* 8 */
  new Config(32, 258, 258, 4096, deflate_slow)     /* 9 max compression */
];


/* ===========================================================================
 * Initialize the "longest match" routines for a new zlib stream
 */
function lm_init(s) {
  s.window_size = 2 * s.w_size;

  /*** CLEAR_HASH(s); ***/
  zero(s.head); // Fill with NIL (= 0);

  /* Set the default configuration parameters:
   */
  s.max_lazy_match = configuration_table[s.level].max_lazy;
  s.good_match = configuration_table[s.level].good_length;
  s.nice_match = configuration_table[s.level].nice_length;
  s.max_chain_length = configuration_table[s.level].max_chain;

  s.strstart = 0;
  s.block_start = 0;
  s.lookahead = 0;
  s.insert = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  s.ins_h = 0;
}


function DeflateState() {
  this.strm = null;            /* pointer back to this zlib stream */
  this.status = 0;            /* as the name implies */
  this.pending_buf = null;      /* output still pending */
  this.pending_buf_size = 0;  /* size of pending_buf */
  this.pending_out = 0;       /* next pending byte to output to the stream */
  this.pending = 0;           /* nb of bytes in the pending buffer */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.gzhead = null;         /* gzip header information to write */
  this.gzindex = 0;           /* where in extra, name, or comment */
  this.method = Z_DEFLATED; /* can only be DEFLATED */
  this.last_flush = -1;   /* value of flush param for previous deflate call */

  this.w_size = 0;  /* LZ77 window size (32K by default) */
  this.w_bits = 0;  /* log2(w_size)  (8..16) */
  this.w_mask = 0;  /* w_size - 1 */

  this.window = null;
  /* Sliding window. Input bytes are read into the second half of the window,
   * and move to the first half later to keep a dictionary of at least wSize
   * bytes. With this organization, matches are limited to a distance of
   * wSize-MAX_MATCH bytes, but this ensures that IO is always
   * performed with a length multiple of the block size.
   */

  this.window_size = 0;
  /* Actual size of window: 2*wSize, except when the user input buffer
   * is directly used as sliding window.
   */

  this.prev = null;
  /* Link to older string with same hash index. To limit the size of this
   * array to 64K, this link is maintained only for the last 32K strings.
   * An index in this array is thus a window index modulo 32K.
   */

  this.head = null;   /* Heads of the hash chains or NIL. */

  this.ins_h = 0;       /* hash index of string to be inserted */
  this.hash_size = 0;   /* number of elements in hash table */
  this.hash_bits = 0;   /* log2(hash_size) */
  this.hash_mask = 0;   /* hash_size-1 */

  this.hash_shift = 0;
  /* Number of bits by which ins_h must be shifted at each input
   * step. It must be such that after MIN_MATCH steps, the oldest
   * byte no longer takes part in the hash key, that is:
   *   hash_shift * MIN_MATCH >= hash_bits
   */

  this.block_start = 0;
  /* Window position at the beginning of the current output block. Gets
   * negative when the window is moved backwards.
   */

  this.match_length = 0;      /* length of best match */
  this.prev_match = 0;        /* previous match */
  this.match_available = 0;   /* set if previous match exists */
  this.strstart = 0;          /* start of string to insert */
  this.match_start = 0;       /* start of matching string */
  this.lookahead = 0;         /* number of valid bytes ahead in window */

  this.prev_length = 0;
  /* Length of the best match at previous step. Matches not greater than this
   * are discarded. This is used in the lazy match evaluation.
   */

  this.max_chain_length = 0;
  /* To speed up deflation, hash chains are never searched beyond this
   * length.  A higher limit improves compression ratio but degrades the
   * speed.
   */

  this.max_lazy_match = 0;
  /* Attempt to find a better match only when the current match is strictly
   * smaller than this value. This mechanism is used only for compression
   * levels >= 4.
   */
  // That's alias to max_lazy_match, don't use directly
  //this.max_insert_length = 0;
  /* Insert new strings in the hash table only if the match length is not
   * greater than this length. This saves time but degrades compression.
   * max_insert_length is used only for compression levels <= 3.
   */

  this.level = 0;     /* compression level (1..9) */
  this.strategy = 0;  /* favor or force Huffman coding*/

  this.good_match = 0;
  /* Use a faster search when the previous match is longer than this */

  this.nice_match = 0; /* Stop searching when current match exceeds this */

              /* used by trees.c: */

  /* Didn't use ct_data typedef below to suppress compiler warning */

  // struct ct_data_s dyn_ltree[HEAP_SIZE];   /* literal and length tree */
  // struct ct_data_s dyn_dtree[2*D_CODES+1]; /* distance tree */
  // struct ct_data_s bl_tree[2*BL_CODES+1];  /* Huffman tree for bit lengths */

  // Use flat array of DOUBLE size, with interleaved fata,
  // because JS does not support effective
  this.dyn_ltree  = new utils.Buf16(HEAP_SIZE * 2);
  this.dyn_dtree  = new utils.Buf16((2 * D_CODES + 1) * 2);
  this.bl_tree    = new utils.Buf16((2 * BL_CODES + 1) * 2);
  zero(this.dyn_ltree);
  zero(this.dyn_dtree);
  zero(this.bl_tree);

  this.l_desc   = null;         /* desc. for literal tree */
  this.d_desc   = null;         /* desc. for distance tree */
  this.bl_desc  = null;         /* desc. for bit length tree */

  //ush bl_count[MAX_BITS+1];
  this.bl_count = new utils.Buf16(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  //int heap[2*L_CODES+1];      /* heap used to build the Huffman trees */
  this.heap = new utils.Buf16(2 * L_CODES + 1);  /* heap used to build the Huffman trees */
  zero(this.heap);

  this.heap_len = 0;               /* number of elements in the heap */
  this.heap_max = 0;               /* element of largest frequency */
  /* The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
   * The same heap array is used to build all trees.
   */

  this.depth = new utils.Buf16(2 * L_CODES + 1); //uch depth[2*L_CODES+1];
  zero(this.depth);
  /* Depth of each subtree used as tie breaker for trees of equal frequency
   */

  this.l_buf = 0;          /* buffer index for literals or lengths */

  this.lit_bufsize = 0;
  /* Size of match buffer for literals/lengths.  There are 4 reasons for
   * limiting lit_bufsize to 64K:
   *   - frequencies can be kept in 16 bit counters
   *   - if compression is not successful for the first block, all input
   *     data is still in the window so we can still emit a stored block even
   *     when input comes from standard input.  (This can also be done for
   *     all blocks if lit_bufsize is not greater than 32K.)
   *   - if compression is not successful for a file smaller than 64K, we can
   *     even emit a stored file instead of a stored block (saving 5 bytes).
   *     This is applicable only for zip (not gzip or zlib).
   *   - creating new Huffman trees less frequently may not provide fast
   *     adaptation to changes in the input data statistics. (Take for
   *     example a binary file with poorly compressible code followed by
   *     a highly compressible string table.) Smaller buffer sizes give
   *     fast adaptation but have of course the overhead of transmitting
   *     trees more frequently.
   *   - I can't count above 4
   */

  this.last_lit = 0;      /* running index in l_buf */

  this.d_buf = 0;
  /* Buffer index for distances. To simplify the code, d_buf and l_buf have
   * the same number of elements. To use different lengths, an extra flag
   * array would be necessary.
   */

  this.opt_len = 0;       /* bit length of current block with optimal trees */
  this.static_len = 0;    /* bit length of current block with static trees */
  this.matches = 0;       /* number of string matches in current block */
  this.insert = 0;        /* bytes at end of window left to insert */


  this.bi_buf = 0;
  /* Output buffer. bits are inserted starting at the bottom (least
   * significant bits).
   */
  this.bi_valid = 0;
  /* Number of valid bits in bi_buf.  All bits above the last valid bit
   * are always zero.
   */

  // Used for window memory init. We safely ignore it for JS. That makes
  // sense only for pointers and memory check tools.
  //this.high_water = 0;
  /* High water mark offset in window for initialized bytes -- bytes above
   * this are set to zero in order to avoid memory check warnings when
   * longest match routines access bytes past the input.  This is then
   * updated to the new high water mark.
   */
}


function deflateResetKeep(strm) {
  var s;

  if (!strm || !strm.state) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.total_in = strm.total_out = 0;
  strm.data_type = Z_UNKNOWN;

  s = strm.state;
  s.pending = 0;
  s.pending_out = 0;

  if (s.wrap < 0) {
    s.wrap = -s.wrap;
    /* was made negative by deflate(..., Z_FINISH); */
  }
  s.status = (s.wrap ? INIT_STATE : BUSY_STATE);
  strm.adler = (s.wrap === 2) ?
    0  // crc32(0, Z_NULL, 0)
  :
    1; // adler32(0, Z_NULL, 0)
  s.last_flush = Z_NO_FLUSH;
  trees._tr_init(s);
  return Z_OK;
}


function deflateReset(strm) {
  var ret = deflateResetKeep(strm);
  if (ret === Z_OK) {
    lm_init(strm.state);
  }
  return ret;
}


function deflateSetHeader(strm, head) {
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  if (strm.state.wrap !== 2) { return Z_STREAM_ERROR; }
  strm.state.gzhead = head;
  return Z_OK;
}


function deflateInit2(strm, level, method, windowBits, memLevel, strategy) {
  if (!strm) { // === Z_NULL
    return Z_STREAM_ERROR;
  }
  var wrap = 1;

  if (level === Z_DEFAULT_COMPRESSION) {
    level = 6;
  }

  if (windowBits < 0) { /* suppress zlib wrapper */
    wrap = 0;
    windowBits = -windowBits;
  }

  else if (windowBits > 15) {
    wrap = 2;           /* write gzip wrapper instead */
    windowBits -= 16;
  }


  if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || method !== Z_DEFLATED ||
    windowBits < 8 || windowBits > 15 || level < 0 || level > 9 ||
    strategy < 0 || strategy > Z_FIXED) {
    return err(strm, Z_STREAM_ERROR);
  }


  if (windowBits === 8) {
    windowBits = 9;
  }
  /* until 256-byte window bug fixed */

  var s = new DeflateState();

  strm.state = s;
  s.strm = strm;

  s.wrap = wrap;
  s.gzhead = null;
  s.w_bits = windowBits;
  s.w_size = 1 << s.w_bits;
  s.w_mask = s.w_size - 1;

  s.hash_bits = memLevel + 7;
  s.hash_size = 1 << s.hash_bits;
  s.hash_mask = s.hash_size - 1;
  s.hash_shift = ~~((s.hash_bits + MIN_MATCH - 1) / MIN_MATCH);

  s.window = new utils.Buf8(s.w_size * 2);
  s.head = new utils.Buf16(s.hash_size);
  s.prev = new utils.Buf16(s.w_size);

  // Don't need mem init magic for JS.
  //s.high_water = 0;  /* nothing written to s->window yet */

  s.lit_bufsize = 1 << (memLevel + 6); /* 16K elements by default */

  s.pending_buf_size = s.lit_bufsize * 4;

  //overlay = (ushf *) ZALLOC(strm, s->lit_bufsize, sizeof(ush)+2);
  //s->pending_buf = (uchf *) overlay;
  s.pending_buf = new utils.Buf8(s.pending_buf_size);

  // It is offset from `s.pending_buf` (size is `s.lit_bufsize * 2`)
  //s->d_buf = overlay + s->lit_bufsize/sizeof(ush);
  s.d_buf = 1 * s.lit_bufsize;

  //s->l_buf = s->pending_buf + (1+sizeof(ush))*s->lit_bufsize;
  s.l_buf = (1 + 2) * s.lit_bufsize;

  s.level = level;
  s.strategy = strategy;
  s.method = method;

  return deflateReset(strm);
}

function deflateInit(strm, level) {
  return deflateInit2(strm, level, Z_DEFLATED, MAX_WBITS, DEF_MEM_LEVEL, Z_DEFAULT_STRATEGY);
}


function deflate(strm, flush) {
  var old_flush, s;
  var beg, val; // for gzip header write only

  if (!strm || !strm.state ||
    flush > Z_BLOCK || flush < 0) {
    return strm ? err(strm, Z_STREAM_ERROR) : Z_STREAM_ERROR;
  }

  s = strm.state;

  if (!strm.output ||
      (!strm.input && strm.avail_in !== 0) ||
      (s.status === FINISH_STATE && flush !== Z_FINISH)) {
    return err(strm, (strm.avail_out === 0) ? Z_BUF_ERROR : Z_STREAM_ERROR);
  }

  s.strm = strm; /* just in case */
  old_flush = s.last_flush;
  s.last_flush = flush;

  /* Write the header */
  if (s.status === INIT_STATE) {

    if (s.wrap === 2) { // GZIP header
      strm.adler = 0;  //crc32(0L, Z_NULL, 0);
      put_byte(s, 31);
      put_byte(s, 139);
      put_byte(s, 8);
      if (!s.gzhead) { // s->gzhead == Z_NULL
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, OS_CODE);
        s.status = BUSY_STATE;
      }
      else {
        put_byte(s, (s.gzhead.text ? 1 : 0) +
                    (s.gzhead.hcrc ? 2 : 0) +
                    (!s.gzhead.extra ? 0 : 4) +
                    (!s.gzhead.name ? 0 : 8) +
                    (!s.gzhead.comment ? 0 : 16)
        );
        put_byte(s, s.gzhead.time & 0xff);
        put_byte(s, (s.gzhead.time >> 8) & 0xff);
        put_byte(s, (s.gzhead.time >> 16) & 0xff);
        put_byte(s, (s.gzhead.time >> 24) & 0xff);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, s.gzhead.os & 0xff);
        if (s.gzhead.extra && s.gzhead.extra.length) {
          put_byte(s, s.gzhead.extra.length & 0xff);
          put_byte(s, (s.gzhead.extra.length >> 8) & 0xff);
        }
        if (s.gzhead.hcrc) {
          strm.adler = crc32(strm.adler, s.pending_buf, s.pending, 0);
        }
        s.gzindex = 0;
        s.status = EXTRA_STATE;
      }
    }
    else // DEFLATE header
    {
      var header = (Z_DEFLATED + ((s.w_bits - 8) << 4)) << 8;
      var level_flags = -1;

      if (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2) {
        level_flags = 0;
      } else if (s.level < 6) {
        level_flags = 1;
      } else if (s.level === 6) {
        level_flags = 2;
      } else {
        level_flags = 3;
      }
      header |= (level_flags << 6);
      if (s.strstart !== 0) { header |= PRESET_DICT; }
      header += 31 - (header % 31);

      s.status = BUSY_STATE;
      putShortMSB(s, header);

      /* Save the adler32 of the preset dictionary: */
      if (s.strstart !== 0) {
        putShortMSB(s, strm.adler >>> 16);
        putShortMSB(s, strm.adler & 0xffff);
      }
      strm.adler = 1; // adler32(0L, Z_NULL, 0);
    }
  }

//#ifdef GZIP
  if (s.status === EXTRA_STATE) {
    if (s.gzhead.extra/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */

      while (s.gzindex < (s.gzhead.extra.length & 0xffff)) {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            break;
          }
        }
        put_byte(s, s.gzhead.extra[s.gzindex] & 0xff);
        s.gzindex++;
      }
      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (s.gzindex === s.gzhead.extra.length) {
        s.gzindex = 0;
        s.status = NAME_STATE;
      }
    }
    else {
      s.status = NAME_STATE;
    }
  }
  if (s.status === NAME_STATE) {
    if (s.gzhead.name/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.name.length) {
          val = s.gzhead.name.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.gzindex = 0;
        s.status = COMMENT_STATE;
      }
    }
    else {
      s.status = COMMENT_STATE;
    }
  }
  if (s.status === COMMENT_STATE) {
    if (s.gzhead.comment/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.comment.length) {
          val = s.gzhead.comment.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.status = HCRC_STATE;
      }
    }
    else {
      s.status = HCRC_STATE;
    }
  }
  if (s.status === HCRC_STATE) {
    if (s.gzhead.hcrc) {
      if (s.pending + 2 > s.pending_buf_size) {
        flush_pending(strm);
      }
      if (s.pending + 2 <= s.pending_buf_size) {
        put_byte(s, strm.adler & 0xff);
        put_byte(s, (strm.adler >> 8) & 0xff);
        strm.adler = 0; //crc32(0L, Z_NULL, 0);
        s.status = BUSY_STATE;
      }
    }
    else {
      s.status = BUSY_STATE;
    }
  }
//#endif

  /* Flush as much pending output as possible */
  if (s.pending !== 0) {
    flush_pending(strm);
    if (strm.avail_out === 0) {
      /* Since avail_out is 0, deflate will be called again with
       * more output space, but possibly with both pending and
       * avail_in equal to zero. There won't be anything to do,
       * but this is not an error situation so make sure we
       * return OK instead of BUF_ERROR at next call of deflate:
       */
      s.last_flush = -1;
      return Z_OK;
    }

    /* Make sure there is something to do and avoid duplicate consecutive
     * flushes. For repeated and useless calls with Z_FINISH, we keep
     * returning Z_STREAM_END instead of Z_BUF_ERROR.
     */
  } else if (strm.avail_in === 0 && rank(flush) <= rank(old_flush) &&
    flush !== Z_FINISH) {
    return err(strm, Z_BUF_ERROR);
  }

  /* User must not provide more input after the first FINISH: */
  if (s.status === FINISH_STATE && strm.avail_in !== 0) {
    return err(strm, Z_BUF_ERROR);
  }

  /* Start a new block or continue the current one.
   */
  if (strm.avail_in !== 0 || s.lookahead !== 0 ||
    (flush !== Z_NO_FLUSH && s.status !== FINISH_STATE)) {
    var bstate = (s.strategy === Z_HUFFMAN_ONLY) ? deflate_huff(s, flush) :
      (s.strategy === Z_RLE ? deflate_rle(s, flush) :
        configuration_table[s.level].func(s, flush));

    if (bstate === BS_FINISH_STARTED || bstate === BS_FINISH_DONE) {
      s.status = FINISH_STATE;
    }
    if (bstate === BS_NEED_MORE || bstate === BS_FINISH_STARTED) {
      if (strm.avail_out === 0) {
        s.last_flush = -1;
        /* avoid BUF_ERROR next call, see above */
      }
      return Z_OK;
      /* If flush != Z_NO_FLUSH && avail_out == 0, the next call
       * of deflate should use the same flush parameter to make sure
       * that the flush is complete. So we don't have to output an
       * empty block here, this will be done at next call. This also
       * ensures that for a very small output buffer, we emit at most
       * one empty block.
       */
    }
    if (bstate === BS_BLOCK_DONE) {
      if (flush === Z_PARTIAL_FLUSH) {
        trees._tr_align(s);
      }
      else if (flush !== Z_BLOCK) { /* FULL_FLUSH or SYNC_FLUSH */

        trees._tr_stored_block(s, 0, 0, false);
        /* For a full flush, this empty block will be recognized
         * as a special marker by inflate_sync().
         */
        if (flush === Z_FULL_FLUSH) {
          /*** CLEAR_HASH(s); ***/             /* forget history */
          zero(s.head); // Fill with NIL (= 0);

          if (s.lookahead === 0) {
            s.strstart = 0;
            s.block_start = 0;
            s.insert = 0;
          }
        }
      }
      flush_pending(strm);
      if (strm.avail_out === 0) {
        s.last_flush = -1; /* avoid BUF_ERROR at next call, see above */
        return Z_OK;
      }
    }
  }
  //Assert(strm->avail_out > 0, "bug2");
  //if (strm.avail_out <= 0) { throw new Error("bug2");}

  if (flush !== Z_FINISH) { return Z_OK; }
  if (s.wrap <= 0) { return Z_STREAM_END; }

  /* Write the trailer */
  if (s.wrap === 2) {
    put_byte(s, strm.adler & 0xff);
    put_byte(s, (strm.adler >> 8) & 0xff);
    put_byte(s, (strm.adler >> 16) & 0xff);
    put_byte(s, (strm.adler >> 24) & 0xff);
    put_byte(s, strm.total_in & 0xff);
    put_byte(s, (strm.total_in >> 8) & 0xff);
    put_byte(s, (strm.total_in >> 16) & 0xff);
    put_byte(s, (strm.total_in >> 24) & 0xff);
  }
  else
  {
    putShortMSB(s, strm.adler >>> 16);
    putShortMSB(s, strm.adler & 0xffff);
  }

  flush_pending(strm);
  /* If avail_out is zero, the application will call deflate again
   * to flush the rest.
   */
  if (s.wrap > 0) { s.wrap = -s.wrap; }
  /* write the trailer only once! */
  return s.pending !== 0 ? Z_OK : Z_STREAM_END;
}

function deflateEnd(strm) {
  var status;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  status = strm.state.status;
  if (status !== INIT_STATE &&
    status !== EXTRA_STATE &&
    status !== NAME_STATE &&
    status !== COMMENT_STATE &&
    status !== HCRC_STATE &&
    status !== BUSY_STATE &&
    status !== FINISH_STATE
  ) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.state = null;

  return status === BUSY_STATE ? err(strm, Z_DATA_ERROR) : Z_OK;
}


/* =========================================================================
 * Initializes the compression dictionary from the given byte
 * sequence without producing any compressed output.
 */
function deflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var s;
  var str, n;
  var wrap;
  var avail;
  var next;
  var input;
  var tmpDict;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  s = strm.state;
  wrap = s.wrap;

  if (wrap === 2 || (wrap === 1 && s.status !== INIT_STATE) || s.lookahead) {
    return Z_STREAM_ERROR;
  }

  /* when using zlib wrappers, compute Adler-32 for provided dictionary */
  if (wrap === 1) {
    /* adler32(strm->adler, dictionary, dictLength); */
    strm.adler = adler32(strm.adler, dictionary, dictLength, 0);
  }

  s.wrap = 0;   /* avoid computing Adler-32 in read_buf */

  /* if dictionary would fill window, just replace the history */
  if (dictLength >= s.w_size) {
    if (wrap === 0) {            /* already empty otherwise */
      /*** CLEAR_HASH(s); ***/
      zero(s.head); // Fill with NIL (= 0);
      s.strstart = 0;
      s.block_start = 0;
      s.insert = 0;
    }
    /* use the tail */
    // dictionary = dictionary.slice(dictLength - s.w_size);
    tmpDict = new utils.Buf8(s.w_size);
    utils.arraySet(tmpDict, dictionary, dictLength - s.w_size, s.w_size, 0);
    dictionary = tmpDict;
    dictLength = s.w_size;
  }
  /* insert dictionary into window and hash */
  avail = strm.avail_in;
  next = strm.next_in;
  input = strm.input;
  strm.avail_in = dictLength;
  strm.next_in = 0;
  strm.input = dictionary;
  fill_window(s);
  while (s.lookahead >= MIN_MATCH) {
    str = s.strstart;
    n = s.lookahead - (MIN_MATCH - 1);
    do {
      /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

      s.prev[str & s.w_mask] = s.head[s.ins_h];

      s.head[s.ins_h] = str;
      str++;
    } while (--n);
    s.strstart = str;
    s.lookahead = MIN_MATCH - 1;
    fill_window(s);
  }
  s.strstart += s.lookahead;
  s.block_start = s.strstart;
  s.insert = s.lookahead;
  s.lookahead = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  strm.next_in = next;
  strm.input = input;
  strm.avail_in = avail;
  s.wrap = wrap;
  return Z_OK;
}


exports.deflateInit = deflateInit;
exports.deflateInit2 = deflateInit2;
exports.deflateReset = deflateReset;
exports.deflateResetKeep = deflateResetKeep;
exports.deflateSetHeader = deflateSetHeader;
exports.deflate = deflate;
exports.deflateEnd = deflateEnd;
exports.deflateSetDictionary = deflateSetDictionary;
exports.deflateInfo = 'pako deflate (from Nodeca project)';

/* Not implemented
exports.deflateBound = deflateBound;
exports.deflateCopy = deflateCopy;
exports.deflateParams = deflateParams;
exports.deflatePending = deflatePending;
exports.deflatePrime = deflatePrime;
exports.deflateTune = deflateTune;
*/


/***/ }),

/***/ 96395:
/***/ (function(module) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// See state defs from inflate.js
var BAD = 30;       /* got a data error -- remain here until reset */
var TYPE = 12;      /* i: waiting for type bits, including last-flag bit */

/*
   Decode literal, length, and distance codes and write out the resulting
   literal and match bytes until either not enough input or output is
   available, an end-of-block is encountered, or a data error is encountered.
   When large enough input and output buffers are supplied to inflate(), for
   example, a 16K input buffer and a 64K output buffer, more than 95% of the
   inflate execution time is spent in this routine.

   Entry assumptions:

        state.mode === LEN
        strm.avail_in >= 6
        strm.avail_out >= 258
        start >= strm.avail_out
        state.bits < 8

   On return, state.mode is one of:

        LEN -- ran out of enough output space or enough available input
        TYPE -- reached end of block code, inflate() to interpret next block
        BAD -- error in block data

   Notes:

    - The maximum input bits used by a length/distance pair is 15 bits for the
      length code, 5 bits for the length extra, 15 bits for the distance code,
      and 13 bits for the distance extra.  This totals 48 bits, or six bytes.
      Therefore if strm.avail_in >= 6, then there is enough input to avoid
      checking for available input while decoding.

    - The maximum bytes that a single length/distance pair can output is 258
      bytes, which is the maximum length that can be coded.  inflate_fast()
      requires strm.avail_out >= 258 for each loop to avoid checking for
      output space.
 */
module.exports = function inflate_fast(strm, start) {
  var state;
  var _in;                    /* local strm.input */
  var last;                   /* have enough input while in < last */
  var _out;                   /* local strm.output */
  var beg;                    /* inflate()'s initial strm.output */
  var end;                    /* while out < end, enough space available */
//#ifdef INFLATE_STRICT
  var dmax;                   /* maximum distance from zlib header */
//#endif
  var wsize;                  /* window size or zero if not using window */
  var whave;                  /* valid bytes in the window */
  var wnext;                  /* window write index */
  // Use `s_window` instead `window`, avoid conflict with instrumentation tools
  var s_window;               /* allocated sliding window, if wsize != 0 */
  var hold;                   /* local strm.hold */
  var bits;                   /* local strm.bits */
  var lcode;                  /* local strm.lencode */
  var dcode;                  /* local strm.distcode */
  var lmask;                  /* mask for first level of length codes */
  var dmask;                  /* mask for first level of distance codes */
  var here;                   /* retrieved table entry */
  var op;                     /* code bits, operation, extra bits, or */
                              /*  window position, window bytes to copy */
  var len;                    /* match length, unused bytes */
  var dist;                   /* match distance */
  var from;                   /* where to copy match from */
  var from_source;


  var input, output; // JS specific, because we have no pointers

  /* copy state to local variables */
  state = strm.state;
  //here = state.here;
  _in = strm.next_in;
  input = strm.input;
  last = _in + (strm.avail_in - 5);
  _out = strm.next_out;
  output = strm.output;
  beg = _out - (start - strm.avail_out);
  end = _out + (strm.avail_out - 257);
//#ifdef INFLATE_STRICT
  dmax = state.dmax;
//#endif
  wsize = state.wsize;
  whave = state.whave;
  wnext = state.wnext;
  s_window = state.window;
  hold = state.hold;
  bits = state.bits;
  lcode = state.lencode;
  dcode = state.distcode;
  lmask = (1 << state.lenbits) - 1;
  dmask = (1 << state.distbits) - 1;


  /* decode literals and length/distances until end-of-block or not enough
     input data or output space */

  top:
  do {
    if (bits < 15) {
      hold += input[_in++] << bits;
      bits += 8;
      hold += input[_in++] << bits;
      bits += 8;
    }

    here = lcode[hold & lmask];

    dolen:
    for (;;) { // Goto emulation
      op = here >>> 24/*here.bits*/;
      hold >>>= op;
      bits -= op;
      op = (here >>> 16) & 0xff/*here.op*/;
      if (op === 0) {                          /* literal */
        //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
        //        "inflate:         literal '%c'\n" :
        //        "inflate:         literal 0x%02x\n", here.val));
        output[_out++] = here & 0xffff/*here.val*/;
      }
      else if (op & 16) {                     /* length base */
        len = here & 0xffff/*here.val*/;
        op &= 15;                           /* number of extra bits */
        if (op) {
          if (bits < op) {
            hold += input[_in++] << bits;
            bits += 8;
          }
          len += hold & ((1 << op) - 1);
          hold >>>= op;
          bits -= op;
        }
        //Tracevv((stderr, "inflate:         length %u\n", len));
        if (bits < 15) {
          hold += input[_in++] << bits;
          bits += 8;
          hold += input[_in++] << bits;
          bits += 8;
        }
        here = dcode[hold & dmask];

        dodist:
        for (;;) { // goto emulation
          op = here >>> 24/*here.bits*/;
          hold >>>= op;
          bits -= op;
          op = (here >>> 16) & 0xff/*here.op*/;

          if (op & 16) {                      /* distance base */
            dist = here & 0xffff/*here.val*/;
            op &= 15;                       /* number of extra bits */
            if (bits < op) {
              hold += input[_in++] << bits;
              bits += 8;
              if (bits < op) {
                hold += input[_in++] << bits;
                bits += 8;
              }
            }
            dist += hold & ((1 << op) - 1);
//#ifdef INFLATE_STRICT
            if (dist > dmax) {
              strm.msg = 'invalid distance too far back';
              state.mode = BAD;
              break top;
            }
//#endif
            hold >>>= op;
            bits -= op;
            //Tracevv((stderr, "inflate:         distance %u\n", dist));
            op = _out - beg;                /* max distance in output */
            if (dist > op) {                /* see if copy from window */
              op = dist - op;               /* distance back in window */
              if (op > whave) {
                if (state.sane) {
                  strm.msg = 'invalid distance too far back';
                  state.mode = BAD;
                  break top;
                }

// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//                if (len <= op - whave) {
//                  do {
//                    output[_out++] = 0;
//                  } while (--len);
//                  continue top;
//                }
//                len -= op - whave;
//                do {
//                  output[_out++] = 0;
//                } while (--op > whave);
//                if (op === 0) {
//                  from = _out - dist;
//                  do {
//                    output[_out++] = output[from++];
//                  } while (--len);
//                  continue top;
//                }
//#endif
              }
              from = 0; // window index
              from_source = s_window;
              if (wnext === 0) {           /* very common case */
                from += wsize - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              else if (wnext < op) {      /* wrap around window */
                from += wsize + wnext - op;
                op -= wnext;
                if (op < len) {         /* some from end of window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = 0;
                  if (wnext < len) {  /* some from start of window */
                    op = wnext;
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = _out - dist;      /* rest from output */
                    from_source = output;
                  }
                }
              }
              else {                      /* contiguous in window */
                from += wnext - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              while (len > 2) {
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                len -= 3;
              }
              if (len) {
                output[_out++] = from_source[from++];
                if (len > 1) {
                  output[_out++] = from_source[from++];
                }
              }
            }
            else {
              from = _out - dist;          /* copy direct from output */
              do {                        /* minimum length is three */
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                len -= 3;
              } while (len > 2);
              if (len) {
                output[_out++] = output[from++];
                if (len > 1) {
                  output[_out++] = output[from++];
                }
              }
            }
          }
          else if ((op & 64) === 0) {          /* 2nd level distance code */
            here = dcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
            continue dodist;
          }
          else {
            strm.msg = 'invalid distance code';
            state.mode = BAD;
            break top;
          }

          break; // need to emulate goto via "continue"
        }
      }
      else if ((op & 64) === 0) {              /* 2nd level length code */
        here = lcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
        continue dolen;
      }
      else if (op & 32) {                     /* end-of-block */
        //Tracevv((stderr, "inflate:         end of block\n"));
        state.mode = TYPE;
        break top;
      }
      else {
        strm.msg = 'invalid literal/length code';
        state.mode = BAD;
        break top;
      }

      break; // need to emulate goto via "continue"
    }
  } while (_in < last && _out < end);

  /* return unused bytes (on entry, bits < 8, so in won't go too far back) */
  len = bits >> 3;
  _in -= len;
  bits -= len << 3;
  hold &= (1 << bits) - 1;

  /* update state and return */
  strm.next_in = _in;
  strm.next_out = _out;
  strm.avail_in = (_in < last ? 5 + (last - _in) : 5 - (_in - last));
  strm.avail_out = (_out < end ? 257 + (end - _out) : 257 - (_out - end));
  state.hold = hold;
  state.bits = bits;
  return;
};


/***/ }),

/***/ 2269:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils         = __webpack_require__(72519);
var adler32       = __webpack_require__(46911);
var crc32         = __webpack_require__(99049);
var inflate_fast  = __webpack_require__(96395);
var inflate_table = __webpack_require__(92920);

var CODES = 0;
var LENS = 1;
var DISTS = 2;

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
//var Z_NO_FLUSH      = 0;
//var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
//var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;

/* The deflate compression method */
var Z_DEFLATED  = 8;


/* STATES ====================================================================*/
/* ===========================================================================*/


var    HEAD = 1;       /* i: waiting for magic header */
var    FLAGS = 2;      /* i: waiting for method and flags (gzip) */
var    TIME = 3;       /* i: waiting for modification time (gzip) */
var    OS = 4;         /* i: waiting for extra flags and operating system (gzip) */
var    EXLEN = 5;      /* i: waiting for extra length (gzip) */
var    EXTRA = 6;      /* i: waiting for extra bytes (gzip) */
var    NAME = 7;       /* i: waiting for end of file name (gzip) */
var    COMMENT = 8;    /* i: waiting for end of comment (gzip) */
var    HCRC = 9;       /* i: waiting for header crc (gzip) */
var    DICTID = 10;    /* i: waiting for dictionary check value */
var    DICT = 11;      /* waiting for inflateSetDictionary() call */
var        TYPE = 12;      /* i: waiting for type bits, including last-flag bit */
var        TYPEDO = 13;    /* i: same, but skip check to exit inflate on new block */
var        STORED = 14;    /* i: waiting for stored size (length and complement) */
var        COPY_ = 15;     /* i/o: same as COPY below, but only first time in */
var        COPY = 16;      /* i/o: waiting for input or output to copy stored block */
var        TABLE = 17;     /* i: waiting for dynamic block table lengths */
var        LENLENS = 18;   /* i: waiting for code length code lengths */
var        CODELENS = 19;  /* i: waiting for length/lit and distance code lengths */
var            LEN_ = 20;      /* i: same as LEN below, but only first time in */
var            LEN = 21;       /* i: waiting for length/lit/eob code */
var            LENEXT = 22;    /* i: waiting for length extra bits */
var            DIST = 23;      /* i: waiting for distance code */
var            DISTEXT = 24;   /* i: waiting for distance extra bits */
var            MATCH = 25;     /* o: waiting for output space to copy string */
var            LIT = 26;       /* o: waiting for output space to write literal */
var    CHECK = 27;     /* i: waiting for 32-bit check value */
var    LENGTH = 28;    /* i: waiting for 32-bit length (gzip) */
var    DONE = 29;      /* finished check, done -- remain here until reset */
var    BAD = 30;       /* got a data error -- remain here until reset */
var    MEM = 31;       /* got an inflate() memory error -- remain here until reset */
var    SYNC = 32;      /* looking for synchronization bytes to restart inflate() */

/* ===========================================================================*/



var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH =  (ENOUGH_LENS+ENOUGH_DISTS);

var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_WBITS = MAX_WBITS;


function zswap32(q) {
  return  (((q >>> 24) & 0xff) +
          ((q >>> 8) & 0xff00) +
          ((q & 0xff00) << 8) +
          ((q & 0xff) << 24));
}


function InflateState() {
  this.mode = 0;             /* current inflate mode */
  this.last = false;          /* true if processing last block */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.havedict = false;      /* true if dictionary provided */
  this.flags = 0;             /* gzip header method and flags (0 if zlib) */
  this.dmax = 0;              /* zlib header max distance (INFLATE_STRICT) */
  this.check = 0;             /* protected copy of check value */
  this.total = 0;             /* protected copy of output count */
  // TODO: may be {}
  this.head = null;           /* where to save gzip header information */

  /* sliding window */
  this.wbits = 0;             /* log base 2 of requested window size */
  this.wsize = 0;             /* window size or zero if not using window */
  this.whave = 0;             /* valid bytes in the window */
  this.wnext = 0;             /* window write index */
  this.window = null;         /* allocated sliding window, if needed */

  /* bit accumulator */
  this.hold = 0;              /* input bit accumulator */
  this.bits = 0;              /* number of bits in "in" */

  /* for string and stored block copying */
  this.length = 0;            /* literal or length of data to copy */
  this.offset = 0;            /* distance back to copy string from */

  /* for table and code decoding */
  this.extra = 0;             /* extra bits needed */

  /* fixed and dynamic code tables */
  this.lencode = null;          /* starting table for length/literal codes */
  this.distcode = null;         /* starting table for distance codes */
  this.lenbits = 0;           /* index bits for lencode */
  this.distbits = 0;          /* index bits for distcode */

  /* dynamic table building */
  this.ncode = 0;             /* number of code length code lengths */
  this.nlen = 0;              /* number of length code lengths */
  this.ndist = 0;             /* number of distance code lengths */
  this.have = 0;              /* number of code lengths in lens[] */
  this.next = null;              /* next available space in codes[] */

  this.lens = new utils.Buf16(320); /* temporary storage for code lengths */
  this.work = new utils.Buf16(288); /* work area for code table building */

  /*
   because we don't have pointers in js, we use lencode and distcode directly
   as buffers so we don't need codes
  */
  //this.codes = new utils.Buf32(ENOUGH);       /* space for code tables */
  this.lendyn = null;              /* dynamic table for length/literal codes (JS specific) */
  this.distdyn = null;             /* dynamic table for distance codes (JS specific) */
  this.sane = 0;                   /* if false, allow invalid distance too far */
  this.back = 0;                   /* bits back of last unprocessed length/lit */
  this.was = 0;                    /* initial length of match */
}

function inflateResetKeep(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  strm.total_in = strm.total_out = state.total = 0;
  strm.msg = ''; /*Z_NULL*/
  if (state.wrap) {       /* to support ill-conceived Java test suite */
    strm.adler = state.wrap & 1;
  }
  state.mode = HEAD;
  state.last = 0;
  state.havedict = 0;
  state.dmax = 32768;
  state.head = null/*Z_NULL*/;
  state.hold = 0;
  state.bits = 0;
  //state.lencode = state.distcode = state.next = state.codes;
  state.lencode = state.lendyn = new utils.Buf32(ENOUGH_LENS);
  state.distcode = state.distdyn = new utils.Buf32(ENOUGH_DISTS);

  state.sane = 1;
  state.back = -1;
  //Tracev((stderr, "inflate: reset\n"));
  return Z_OK;
}

function inflateReset(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  state.wsize = 0;
  state.whave = 0;
  state.wnext = 0;
  return inflateResetKeep(strm);

}

function inflateReset2(strm, windowBits) {
  var wrap;
  var state;

  /* get the state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;

  /* extract wrap request from windowBits parameter */
  if (windowBits < 0) {
    wrap = 0;
    windowBits = -windowBits;
  }
  else {
    wrap = (windowBits >> 4) + 1;
    if (windowBits < 48) {
      windowBits &= 15;
    }
  }

  /* set number of window bits, free window if different */
  if (windowBits && (windowBits < 8 || windowBits > 15)) {
    return Z_STREAM_ERROR;
  }
  if (state.window !== null && state.wbits !== windowBits) {
    state.window = null;
  }

  /* update state and reset the rest of it */
  state.wrap = wrap;
  state.wbits = windowBits;
  return inflateReset(strm);
}

function inflateInit2(strm, windowBits) {
  var ret;
  var state;

  if (!strm) { return Z_STREAM_ERROR; }
  //strm.msg = Z_NULL;                 /* in case we return an error */

  state = new InflateState();

  //if (state === Z_NULL) return Z_MEM_ERROR;
  //Tracev((stderr, "inflate: allocated\n"));
  strm.state = state;
  state.window = null/*Z_NULL*/;
  ret = inflateReset2(strm, windowBits);
  if (ret !== Z_OK) {
    strm.state = null/*Z_NULL*/;
  }
  return ret;
}

function inflateInit(strm) {
  return inflateInit2(strm, DEF_WBITS);
}


/*
 Return state with length and distance decoding tables and index sizes set to
 fixed code decoding.  Normally this returns fixed tables from inffixed.h.
 If BUILDFIXED is defined, then instead this routine builds the tables the
 first time it's called, and returns those tables the first time and
 thereafter.  This reduces the size of the code by about 2K bytes, in
 exchange for a little execution time.  However, BUILDFIXED should not be
 used for threaded applications, since the rewriting of the tables and virgin
 may not be thread-safe.
 */
var virgin = true;

var lenfix, distfix; // We have no pointers in JS, so keep tables separate

function fixedtables(state) {
  /* build fixed huffman tables if first call (may not be thread safe) */
  if (virgin) {
    var sym;

    lenfix = new utils.Buf32(512);
    distfix = new utils.Buf32(32);

    /* literal/length table */
    sym = 0;
    while (sym < 144) { state.lens[sym++] = 8; }
    while (sym < 256) { state.lens[sym++] = 9; }
    while (sym < 280) { state.lens[sym++] = 7; }
    while (sym < 288) { state.lens[sym++] = 8; }

    inflate_table(LENS,  state.lens, 0, 288, lenfix,   0, state.work, { bits: 9 });

    /* distance table */
    sym = 0;
    while (sym < 32) { state.lens[sym++] = 5; }

    inflate_table(DISTS, state.lens, 0, 32,   distfix, 0, state.work, { bits: 5 });

    /* do this just once */
    virgin = false;
  }

  state.lencode = lenfix;
  state.lenbits = 9;
  state.distcode = distfix;
  state.distbits = 5;
}


/*
 Update the window with the last wsize (normally 32K) bytes written before
 returning.  If window does not exist yet, create it.  This is only called
 when a window is already in use, or when output has been written during this
 inflate call, but the end of the deflate stream has not been reached yet.
 It is also called to create a window for dictionary data when a dictionary
 is loaded.

 Providing output buffers larger than 32K to inflate() should provide a speed
 advantage, since only the last 32K of output is copied to the sliding window
 upon return from inflate(), and since all distances after the first 32K of
 output will fall in the output data, making match copies simpler and faster.
 The advantage may be dependent on the size of the processor's data caches.
 */
function updatewindow(strm, src, end, copy) {
  var dist;
  var state = strm.state;

  /* if it hasn't been done already, allocate space for the window */
  if (state.window === null) {
    state.wsize = 1 << state.wbits;
    state.wnext = 0;
    state.whave = 0;

    state.window = new utils.Buf8(state.wsize);
  }

  /* copy state->wsize or less output bytes into the circular window */
  if (copy >= state.wsize) {
    utils.arraySet(state.window, src, end - state.wsize, state.wsize, 0);
    state.wnext = 0;
    state.whave = state.wsize;
  }
  else {
    dist = state.wsize - state.wnext;
    if (dist > copy) {
      dist = copy;
    }
    //zmemcpy(state->window + state->wnext, end - copy, dist);
    utils.arraySet(state.window, src, end - copy, dist, state.wnext);
    copy -= dist;
    if (copy) {
      //zmemcpy(state->window, end - copy, copy);
      utils.arraySet(state.window, src, end - copy, copy, 0);
      state.wnext = copy;
      state.whave = state.wsize;
    }
    else {
      state.wnext += dist;
      if (state.wnext === state.wsize) { state.wnext = 0; }
      if (state.whave < state.wsize) { state.whave += dist; }
    }
  }
  return 0;
}

function inflate(strm, flush) {
  var state;
  var input, output;          // input/output buffers
  var next;                   /* next input INDEX */
  var put;                    /* next output INDEX */
  var have, left;             /* available input and output */
  var hold;                   /* bit buffer */
  var bits;                   /* bits in bit buffer */
  var _in, _out;              /* save starting available input and output */
  var copy;                   /* number of stored or match bytes to copy */
  var from;                   /* where to copy match bytes from */
  var from_source;
  var here = 0;               /* current decoding table entry */
  var here_bits, here_op, here_val; // paked "here" denormalized (JS specific)
  //var last;                   /* parent table entry */
  var last_bits, last_op, last_val; // paked "last" denormalized (JS specific)
  var len;                    /* length to copy for repeats, bits to drop */
  var ret;                    /* return code */
  var hbuf = new utils.Buf8(4);    /* buffer for gzip header crc calculation */
  var opts;

  var n; // temporary var for NEED_BITS

  var order = /* permutation of code lengths */
    [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];


  if (!strm || !strm.state || !strm.output ||
      (!strm.input && strm.avail_in !== 0)) {
    return Z_STREAM_ERROR;
  }

  state = strm.state;
  if (state.mode === TYPE) { state.mode = TYPEDO; }    /* skip check */


  //--- LOAD() ---
  put = strm.next_out;
  output = strm.output;
  left = strm.avail_out;
  next = strm.next_in;
  input = strm.input;
  have = strm.avail_in;
  hold = state.hold;
  bits = state.bits;
  //---

  _in = have;
  _out = left;
  ret = Z_OK;

  inf_leave: // goto emulation
  for (;;) {
    switch (state.mode) {
      case HEAD:
        if (state.wrap === 0) {
          state.mode = TYPEDO;
          break;
        }
        //=== NEEDBITS(16);
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if ((state.wrap & 2) && hold === 0x8b1f) {  /* gzip header */
          state.check = 0/*crc32(0L, Z_NULL, 0)*/;
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//

          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          state.mode = FLAGS;
          break;
        }
        state.flags = 0;           /* expect zlib header */
        if (state.head) {
          state.head.done = false;
        }
        if (!(state.wrap & 1) ||   /* check if zlib header allowed */
          (((hold & 0xff)/*BITS(8)*/ << 8) + (hold >> 8)) % 31) {
          strm.msg = 'incorrect header check';
          state.mode = BAD;
          break;
        }
        if ((hold & 0x0f)/*BITS(4)*/ !== Z_DEFLATED) {
          strm.msg = 'unknown compression method';
          state.mode = BAD;
          break;
        }
        //--- DROPBITS(4) ---//
        hold >>>= 4;
        bits -= 4;
        //---//
        len = (hold & 0x0f)/*BITS(4)*/ + 8;
        if (state.wbits === 0) {
          state.wbits = len;
        }
        else if (len > state.wbits) {
          strm.msg = 'invalid window size';
          state.mode = BAD;
          break;
        }
        state.dmax = 1 << len;
        //Tracev((stderr, "inflate:   zlib header ok\n"));
        strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
        state.mode = hold & 0x200 ? DICTID : TYPE;
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        break;
      case FLAGS:
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.flags = hold;
        if ((state.flags & 0xff) !== Z_DEFLATED) {
          strm.msg = 'unknown compression method';
          state.mode = BAD;
          break;
        }
        if (state.flags & 0xe000) {
          strm.msg = 'unknown header flags set';
          state.mode = BAD;
          break;
        }
        if (state.head) {
          state.head.text = ((hold >> 8) & 1);
        }
        if (state.flags & 0x0200) {
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = TIME;
        /* falls through */
      case TIME:
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (state.head) {
          state.head.time = hold;
        }
        if (state.flags & 0x0200) {
          //=== CRC4(state.check, hold)
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          hbuf[2] = (hold >>> 16) & 0xff;
          hbuf[3] = (hold >>> 24) & 0xff;
          state.check = crc32(state.check, hbuf, 4, 0);
          //===
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = OS;
        /* falls through */
      case OS:
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (state.head) {
          state.head.xflags = (hold & 0xff);
          state.head.os = (hold >> 8);
        }
        if (state.flags & 0x0200) {
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = EXLEN;
        /* falls through */
      case EXLEN:
        if (state.flags & 0x0400) {
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.length = hold;
          if (state.head) {
            state.head.extra_len = hold;
          }
          if (state.flags & 0x0200) {
            //=== CRC2(state.check, hold);
            hbuf[0] = hold & 0xff;
            hbuf[1] = (hold >>> 8) & 0xff;
            state.check = crc32(state.check, hbuf, 2, 0);
            //===//
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
        }
        else if (state.head) {
          state.head.extra = null/*Z_NULL*/;
        }
        state.mode = EXTRA;
        /* falls through */
      case EXTRA:
        if (state.flags & 0x0400) {
          copy = state.length;
          if (copy > have) { copy = have; }
          if (copy) {
            if (state.head) {
              len = state.head.extra_len - state.length;
              if (!state.head.extra) {
                // Use untyped array for more convenient processing later
                state.head.extra = new Array(state.head.extra_len);
              }
              utils.arraySet(
                state.head.extra,
                input,
                next,
                // extra field is limited to 65536 bytes
                // - no need for additional size check
                copy,
                /*len + copy > state.head.extra_max - len ? state.head.extra_max : copy,*/
                len
              );
              //zmemcpy(state.head.extra + len, next,
              //        len + copy > state.head.extra_max ?
              //        state.head.extra_max - len : copy);
            }
            if (state.flags & 0x0200) {
              state.check = crc32(state.check, input, copy, next);
            }
            have -= copy;
            next += copy;
            state.length -= copy;
          }
          if (state.length) { break inf_leave; }
        }
        state.length = 0;
        state.mode = NAME;
        /* falls through */
      case NAME:
        if (state.flags & 0x0800) {
          if (have === 0) { break inf_leave; }
          copy = 0;
          do {
            // TODO: 2 or 1 bytes?
            len = input[next + copy++];
            /* use constant limit because in js we should not preallocate memory */
            if (state.head && len &&
                (state.length < 65536 /*state.head.name_max*/)) {
              state.head.name += String.fromCharCode(len);
            }
          } while (len && copy < have);

          if (state.flags & 0x0200) {
            state.check = crc32(state.check, input, copy, next);
          }
          have -= copy;
          next += copy;
          if (len) { break inf_leave; }
        }
        else if (state.head) {
          state.head.name = null;
        }
        state.length = 0;
        state.mode = COMMENT;
        /* falls through */
      case COMMENT:
        if (state.flags & 0x1000) {
          if (have === 0) { break inf_leave; }
          copy = 0;
          do {
            len = input[next + copy++];
            /* use constant limit because in js we should not preallocate memory */
            if (state.head && len &&
                (state.length < 65536 /*state.head.comm_max*/)) {
              state.head.comment += String.fromCharCode(len);
            }
          } while (len && copy < have);
          if (state.flags & 0x0200) {
            state.check = crc32(state.check, input, copy, next);
          }
          have -= copy;
          next += copy;
          if (len) { break inf_leave; }
        }
        else if (state.head) {
          state.head.comment = null;
        }
        state.mode = HCRC;
        /* falls through */
      case HCRC:
        if (state.flags & 0x0200) {
          //=== NEEDBITS(16); */
          while (bits < 16) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (hold !== (state.check & 0xffff)) {
            strm.msg = 'header crc mismatch';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
        }
        if (state.head) {
          state.head.hcrc = ((state.flags >> 9) & 1);
          state.head.done = true;
        }
        strm.adler = state.check = 0;
        state.mode = TYPE;
        break;
      case DICTID:
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        strm.adler = state.check = zswap32(hold);
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = DICT;
        /* falls through */
      case DICT:
        if (state.havedict === 0) {
          //--- RESTORE() ---
          strm.next_out = put;
          strm.avail_out = left;
          strm.next_in = next;
          strm.avail_in = have;
          state.hold = hold;
          state.bits = bits;
          //---
          return Z_NEED_DICT;
        }
        strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
        state.mode = TYPE;
        /* falls through */
      case TYPE:
        if (flush === Z_BLOCK || flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case TYPEDO:
        if (state.last) {
          //--- BYTEBITS() ---//
          hold >>>= bits & 7;
          bits -= bits & 7;
          //---//
          state.mode = CHECK;
          break;
        }
        //=== NEEDBITS(3); */
        while (bits < 3) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.last = (hold & 0x01)/*BITS(1)*/;
        //--- DROPBITS(1) ---//
        hold >>>= 1;
        bits -= 1;
        //---//

        switch ((hold & 0x03)/*BITS(2)*/) {
          case 0:                             /* stored block */
            //Tracev((stderr, "inflate:     stored block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = STORED;
            break;
          case 1:                             /* fixed block */
            fixedtables(state);
            //Tracev((stderr, "inflate:     fixed codes block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = LEN_;             /* decode codes */
            if (flush === Z_TREES) {
              //--- DROPBITS(2) ---//
              hold >>>= 2;
              bits -= 2;
              //---//
              break inf_leave;
            }
            break;
          case 2:                             /* dynamic block */
            //Tracev((stderr, "inflate:     dynamic codes block%s\n",
            //        state.last ? " (last)" : ""));
            state.mode = TABLE;
            break;
          case 3:
            strm.msg = 'invalid block type';
            state.mode = BAD;
        }
        //--- DROPBITS(2) ---//
        hold >>>= 2;
        bits -= 2;
        //---//
        break;
      case STORED:
        //--- BYTEBITS() ---// /* go to byte boundary */
        hold >>>= bits & 7;
        bits -= bits & 7;
        //---//
        //=== NEEDBITS(32); */
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if ((hold & 0xffff) !== ((hold >>> 16) ^ 0xffff)) {
          strm.msg = 'invalid stored block lengths';
          state.mode = BAD;
          break;
        }
        state.length = hold & 0xffff;
        //Tracev((stderr, "inflate:       stored length %u\n",
        //        state.length));
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = COPY_;
        if (flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case COPY_:
        state.mode = COPY;
        /* falls through */
      case COPY:
        copy = state.length;
        if (copy) {
          if (copy > have) { copy = have; }
          if (copy > left) { copy = left; }
          if (copy === 0) { break inf_leave; }
          //--- zmemcpy(put, next, copy); ---
          utils.arraySet(output, input, next, copy, put);
          //---//
          have -= copy;
          next += copy;
          left -= copy;
          put += copy;
          state.length -= copy;
          break;
        }
        //Tracev((stderr, "inflate:       stored end\n"));
        state.mode = TYPE;
        break;
      case TABLE:
        //=== NEEDBITS(14); */
        while (bits < 14) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.nlen = (hold & 0x1f)/*BITS(5)*/ + 257;
        //--- DROPBITS(5) ---//
        hold >>>= 5;
        bits -= 5;
        //---//
        state.ndist = (hold & 0x1f)/*BITS(5)*/ + 1;
        //--- DROPBITS(5) ---//
        hold >>>= 5;
        bits -= 5;
        //---//
        state.ncode = (hold & 0x0f)/*BITS(4)*/ + 4;
        //--- DROPBITS(4) ---//
        hold >>>= 4;
        bits -= 4;
        //---//
//#ifndef PKZIP_BUG_WORKAROUND
        if (state.nlen > 286 || state.ndist > 30) {
          strm.msg = 'too many length or distance symbols';
          state.mode = BAD;
          break;
        }
//#endif
        //Tracev((stderr, "inflate:       table sizes ok\n"));
        state.have = 0;
        state.mode = LENLENS;
        /* falls through */
      case LENLENS:
        while (state.have < state.ncode) {
          //=== NEEDBITS(3);
          while (bits < 3) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.lens[order[state.have++]] = (hold & 0x07);//BITS(3);
          //--- DROPBITS(3) ---//
          hold >>>= 3;
          bits -= 3;
          //---//
        }
        while (state.have < 19) {
          state.lens[order[state.have++]] = 0;
        }
        // We have separate tables & no pointers. 2 commented lines below not needed.
        //state.next = state.codes;
        //state.lencode = state.next;
        // Switch to use dynamic table
        state.lencode = state.lendyn;
        state.lenbits = 7;

        opts = { bits: state.lenbits };
        ret = inflate_table(CODES, state.lens, 0, 19, state.lencode, 0, state.work, opts);
        state.lenbits = opts.bits;

        if (ret) {
          strm.msg = 'invalid code lengths set';
          state.mode = BAD;
          break;
        }
        //Tracev((stderr, "inflate:       code lengths ok\n"));
        state.have = 0;
        state.mode = CODELENS;
        /* falls through */
      case CODELENS:
        while (state.have < state.nlen + state.ndist) {
          for (;;) {
            here = state.lencode[hold & ((1 << state.lenbits) - 1)];/*BITS(state.lenbits)*/
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          if (here_val < 16) {
            //--- DROPBITS(here.bits) ---//
            hold >>>= here_bits;
            bits -= here_bits;
            //---//
            state.lens[state.have++] = here_val;
          }
          else {
            if (here_val === 16) {
              //=== NEEDBITS(here.bits + 2);
              n = here_bits + 2;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              if (state.have === 0) {
                strm.msg = 'invalid bit length repeat';
                state.mode = BAD;
                break;
              }
              len = state.lens[state.have - 1];
              copy = 3 + (hold & 0x03);//BITS(2);
              //--- DROPBITS(2) ---//
              hold >>>= 2;
              bits -= 2;
              //---//
            }
            else if (here_val === 17) {
              //=== NEEDBITS(here.bits + 3);
              n = here_bits + 3;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              len = 0;
              copy = 3 + (hold & 0x07);//BITS(3);
              //--- DROPBITS(3) ---//
              hold >>>= 3;
              bits -= 3;
              //---//
            }
            else {
              //=== NEEDBITS(here.bits + 7);
              n = here_bits + 7;
              while (bits < n) {
                if (have === 0) { break inf_leave; }
                have--;
                hold += input[next++] << bits;
                bits += 8;
              }
              //===//
              //--- DROPBITS(here.bits) ---//
              hold >>>= here_bits;
              bits -= here_bits;
              //---//
              len = 0;
              copy = 11 + (hold & 0x7f);//BITS(7);
              //--- DROPBITS(7) ---//
              hold >>>= 7;
              bits -= 7;
              //---//
            }
            if (state.have + copy > state.nlen + state.ndist) {
              strm.msg = 'invalid bit length repeat';
              state.mode = BAD;
              break;
            }
            while (copy--) {
              state.lens[state.have++] = len;
            }
          }
        }

        /* handle error breaks in while */
        if (state.mode === BAD) { break; }

        /* check for end-of-block code (better have one) */
        if (state.lens[256] === 0) {
          strm.msg = 'invalid code -- missing end-of-block';
          state.mode = BAD;
          break;
        }

        /* build code tables -- note: do not change the lenbits or distbits
           values here (9 and 6) without reading the comments in inftrees.h
           concerning the ENOUGH constants, which depend on those values */
        state.lenbits = 9;

        opts = { bits: state.lenbits };
        ret = inflate_table(LENS, state.lens, 0, state.nlen, state.lencode, 0, state.work, opts);
        // We have separate tables & no pointers. 2 commented lines below not needed.
        // state.next_index = opts.table_index;
        state.lenbits = opts.bits;
        // state.lencode = state.next;

        if (ret) {
          strm.msg = 'invalid literal/lengths set';
          state.mode = BAD;
          break;
        }

        state.distbits = 6;
        //state.distcode.copy(state.codes);
        // Switch to use dynamic table
        state.distcode = state.distdyn;
        opts = { bits: state.distbits };
        ret = inflate_table(DISTS, state.lens, state.nlen, state.ndist, state.distcode, 0, state.work, opts);
        // We have separate tables & no pointers. 2 commented lines below not needed.
        // state.next_index = opts.table_index;
        state.distbits = opts.bits;
        // state.distcode = state.next;

        if (ret) {
          strm.msg = 'invalid distances set';
          state.mode = BAD;
          break;
        }
        //Tracev((stderr, 'inflate:       codes ok\n'));
        state.mode = LEN_;
        if (flush === Z_TREES) { break inf_leave; }
        /* falls through */
      case LEN_:
        state.mode = LEN;
        /* falls through */
      case LEN:
        if (have >= 6 && left >= 258) {
          //--- RESTORE() ---
          strm.next_out = put;
          strm.avail_out = left;
          strm.next_in = next;
          strm.avail_in = have;
          state.hold = hold;
          state.bits = bits;
          //---
          inflate_fast(strm, _out);
          //--- LOAD() ---
          put = strm.next_out;
          output = strm.output;
          left = strm.avail_out;
          next = strm.next_in;
          input = strm.input;
          have = strm.avail_in;
          hold = state.hold;
          bits = state.bits;
          //---

          if (state.mode === TYPE) {
            state.back = -1;
          }
          break;
        }
        state.back = 0;
        for (;;) {
          here = state.lencode[hold & ((1 << state.lenbits) - 1)];  /*BITS(state.lenbits)*/
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if (here_bits <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        if (here_op && (here_op & 0xf0) === 0) {
          last_bits = here_bits;
          last_op = here_op;
          last_val = here_val;
          for (;;) {
            here = state.lencode[last_val +
                    ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((last_bits + here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          //--- DROPBITS(last.bits) ---//
          hold >>>= last_bits;
          bits -= last_bits;
          //---//
          state.back += last_bits;
        }
        //--- DROPBITS(here.bits) ---//
        hold >>>= here_bits;
        bits -= here_bits;
        //---//
        state.back += here_bits;
        state.length = here_val;
        if (here_op === 0) {
          //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
          //        "inflate:         literal '%c'\n" :
          //        "inflate:         literal 0x%02x\n", here.val));
          state.mode = LIT;
          break;
        }
        if (here_op & 32) {
          //Tracevv((stderr, "inflate:         end of block\n"));
          state.back = -1;
          state.mode = TYPE;
          break;
        }
        if (here_op & 64) {
          strm.msg = 'invalid literal/length code';
          state.mode = BAD;
          break;
        }
        state.extra = here_op & 15;
        state.mode = LENEXT;
        /* falls through */
      case LENEXT:
        if (state.extra) {
          //=== NEEDBITS(state.extra);
          n = state.extra;
          while (bits < n) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.length += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
          //--- DROPBITS(state.extra) ---//
          hold >>>= state.extra;
          bits -= state.extra;
          //---//
          state.back += state.extra;
        }
        //Tracevv((stderr, "inflate:         length %u\n", state.length));
        state.was = state.length;
        state.mode = DIST;
        /* falls through */
      case DIST:
        for (;;) {
          here = state.distcode[hold & ((1 << state.distbits) - 1)];/*BITS(state.distbits)*/
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if ((here_bits) <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        if ((here_op & 0xf0) === 0) {
          last_bits = here_bits;
          last_op = here_op;
          last_val = here_val;
          for (;;) {
            here = state.distcode[last_val +
                    ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
            here_bits = here >>> 24;
            here_op = (here >>> 16) & 0xff;
            here_val = here & 0xffff;

            if ((last_bits + here_bits) <= bits) { break; }
            //--- PULLBYTE() ---//
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
            //---//
          }
          //--- DROPBITS(last.bits) ---//
          hold >>>= last_bits;
          bits -= last_bits;
          //---//
          state.back += last_bits;
        }
        //--- DROPBITS(here.bits) ---//
        hold >>>= here_bits;
        bits -= here_bits;
        //---//
        state.back += here_bits;
        if (here_op & 64) {
          strm.msg = 'invalid distance code';
          state.mode = BAD;
          break;
        }
        state.offset = here_val;
        state.extra = (here_op) & 15;
        state.mode = DISTEXT;
        /* falls through */
      case DISTEXT:
        if (state.extra) {
          //=== NEEDBITS(state.extra);
          n = state.extra;
          while (bits < n) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          state.offset += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
          //--- DROPBITS(state.extra) ---//
          hold >>>= state.extra;
          bits -= state.extra;
          //---//
          state.back += state.extra;
        }
//#ifdef INFLATE_STRICT
        if (state.offset > state.dmax) {
          strm.msg = 'invalid distance too far back';
          state.mode = BAD;
          break;
        }
//#endif
        //Tracevv((stderr, "inflate:         distance %u\n", state.offset));
        state.mode = MATCH;
        /* falls through */
      case MATCH:
        if (left === 0) { break inf_leave; }
        copy = _out - left;
        if (state.offset > copy) {         /* copy from window */
          copy = state.offset - copy;
          if (copy > state.whave) {
            if (state.sane) {
              strm.msg = 'invalid distance too far back';
              state.mode = BAD;
              break;
            }
// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//          Trace((stderr, "inflate.c too far\n"));
//          copy -= state.whave;
//          if (copy > state.length) { copy = state.length; }
//          if (copy > left) { copy = left; }
//          left -= copy;
//          state.length -= copy;
//          do {
//            output[put++] = 0;
//          } while (--copy);
//          if (state.length === 0) { state.mode = LEN; }
//          break;
//#endif
          }
          if (copy > state.wnext) {
            copy -= state.wnext;
            from = state.wsize - copy;
          }
          else {
            from = state.wnext - copy;
          }
          if (copy > state.length) { copy = state.length; }
          from_source = state.window;
        }
        else {                              /* copy from output */
          from_source = output;
          from = put - state.offset;
          copy = state.length;
        }
        if (copy > left) { copy = left; }
        left -= copy;
        state.length -= copy;
        do {
          output[put++] = from_source[from++];
        } while (--copy);
        if (state.length === 0) { state.mode = LEN; }
        break;
      case LIT:
        if (left === 0) { break inf_leave; }
        output[put++] = state.length;
        left--;
        state.mode = LEN;
        break;
      case CHECK:
        if (state.wrap) {
          //=== NEEDBITS(32);
          while (bits < 32) {
            if (have === 0) { break inf_leave; }
            have--;
            // Use '|' instead of '+' to make sure that result is signed
            hold |= input[next++] << bits;
            bits += 8;
          }
          //===//
          _out -= left;
          strm.total_out += _out;
          state.total += _out;
          if (_out) {
            strm.adler = state.check =
                /*UPDATE(state.check, put - _out, _out);*/
                (state.flags ? crc32(state.check, output, _out, put - _out) : adler32(state.check, output, _out, put - _out));

          }
          _out = left;
          // NB: crc32 stored as signed 32-bit int, zswap32 returns signed too
          if ((state.flags ? hold : zswap32(hold)) !== state.check) {
            strm.msg = 'incorrect data check';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          //Tracev((stderr, "inflate:   check matches trailer\n"));
        }
        state.mode = LENGTH;
        /* falls through */
      case LENGTH:
        if (state.wrap && state.flags) {
          //=== NEEDBITS(32);
          while (bits < 32) {
            if (have === 0) { break inf_leave; }
            have--;
            hold += input[next++] << bits;
            bits += 8;
          }
          //===//
          if (hold !== (state.total & 0xffffffff)) {
            strm.msg = 'incorrect length check';
            state.mode = BAD;
            break;
          }
          //=== INITBITS();
          hold = 0;
          bits = 0;
          //===//
          //Tracev((stderr, "inflate:   length matches trailer\n"));
        }
        state.mode = DONE;
        /* falls through */
      case DONE:
        ret = Z_STREAM_END;
        break inf_leave;
      case BAD:
        ret = Z_DATA_ERROR;
        break inf_leave;
      case MEM:
        return Z_MEM_ERROR;
      case SYNC:
        /* falls through */
      default:
        return Z_STREAM_ERROR;
    }
  }

  // inf_leave <- here is real place for "goto inf_leave", emulated via "break inf_leave"

  /*
     Return from inflate(), updating the total counts and the check value.
     If there was no progress during the inflate() call, return a buffer
     error.  Call updatewindow() to create and/or update the window state.
     Note: a memory error from inflate() is non-recoverable.
   */

  //--- RESTORE() ---
  strm.next_out = put;
  strm.avail_out = left;
  strm.next_in = next;
  strm.avail_in = have;
  state.hold = hold;
  state.bits = bits;
  //---

  if (state.wsize || (_out !== strm.avail_out && state.mode < BAD &&
                      (state.mode < CHECK || flush !== Z_FINISH))) {
    if (updatewindow(strm, strm.output, strm.next_out, _out - strm.avail_out)) {
      state.mode = MEM;
      return Z_MEM_ERROR;
    }
  }
  _in -= strm.avail_in;
  _out -= strm.avail_out;
  strm.total_in += _in;
  strm.total_out += _out;
  state.total += _out;
  if (state.wrap && _out) {
    strm.adler = state.check = /*UPDATE(state.check, strm.next_out - _out, _out);*/
      (state.flags ? crc32(state.check, output, _out, strm.next_out - _out) : adler32(state.check, output, _out, strm.next_out - _out));
  }
  strm.data_type = state.bits + (state.last ? 64 : 0) +
                    (state.mode === TYPE ? 128 : 0) +
                    (state.mode === LEN_ || state.mode === COPY_ ? 256 : 0);
  if (((_in === 0 && _out === 0) || flush === Z_FINISH) && ret === Z_OK) {
    ret = Z_BUF_ERROR;
  }
  return ret;
}

function inflateEnd(strm) {

  if (!strm || !strm.state /*|| strm->zfree == (free_func)0*/) {
    return Z_STREAM_ERROR;
  }

  var state = strm.state;
  if (state.window) {
    state.window = null;
  }
  strm.state = null;
  return Z_OK;
}

function inflateGetHeader(strm, head) {
  var state;

  /* check state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  if ((state.wrap & 2) === 0) { return Z_STREAM_ERROR; }

  /* save header structure */
  state.head = head;
  head.done = false;
  return Z_OK;
}

function inflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var state;
  var dictid;
  var ret;

  /* check state */
  if (!strm /* == Z_NULL */ || !strm.state /* == Z_NULL */) { return Z_STREAM_ERROR; }
  state = strm.state;

  if (state.wrap !== 0 && state.mode !== DICT) {
    return Z_STREAM_ERROR;
  }

  /* check for correct dictionary identifier */
  if (state.mode === DICT) {
    dictid = 1; /* adler32(0, null, 0)*/
    /* dictid = adler32(dictid, dictionary, dictLength); */
    dictid = adler32(dictid, dictionary, dictLength, 0);
    if (dictid !== state.check) {
      return Z_DATA_ERROR;
    }
  }
  /* copy dictionary to window using updatewindow(), which will amend the
   existing dictionary if appropriate */
  ret = updatewindow(strm, dictionary, dictLength, dictLength);
  if (ret) {
    state.mode = MEM;
    return Z_MEM_ERROR;
  }
  state.havedict = 1;
  // Tracev((stderr, "inflate:   dictionary set\n"));
  return Z_OK;
}

exports.inflateReset = inflateReset;
exports.inflateReset2 = inflateReset2;
exports.inflateResetKeep = inflateResetKeep;
exports.inflateInit = inflateInit;
exports.inflateInit2 = inflateInit2;
exports.inflate = inflate;
exports.inflateEnd = inflateEnd;
exports.inflateGetHeader = inflateGetHeader;
exports.inflateSetDictionary = inflateSetDictionary;
exports.inflateInfo = 'pako inflate (from Nodeca project)';

/* Not implemented
exports.inflateCopy = inflateCopy;
exports.inflateGetDictionary = inflateGetDictionary;
exports.inflateMark = inflateMark;
exports.inflatePrime = inflatePrime;
exports.inflateSync = inflateSync;
exports.inflateSyncPoint = inflateSyncPoint;
exports.inflateUndermine = inflateUndermine;
*/


/***/ }),

/***/ 92920:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils = __webpack_require__(72519);

var MAXBITS = 15;
var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH = (ENOUGH_LENS+ENOUGH_DISTS);

var CODES = 0;
var LENS = 1;
var DISTS = 2;

var lbase = [ /* Length codes 257..285 base */
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31,
  35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258, 0, 0
];

var lext = [ /* Length codes 257..285 extra */
  16, 16, 16, 16, 16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18,
  19, 19, 19, 19, 20, 20, 20, 20, 21, 21, 21, 21, 16, 72, 78
];

var dbase = [ /* Distance codes 0..29 base */
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193,
  257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145,
  8193, 12289, 16385, 24577, 0, 0
];

var dext = [ /* Distance codes 0..29 extra */
  16, 16, 16, 16, 17, 17, 18, 18, 19, 19, 20, 20, 21, 21, 22, 22,
  23, 23, 24, 24, 25, 25, 26, 26, 27, 27,
  28, 28, 29, 29, 64, 64
];

module.exports = function inflate_table(type, lens, lens_index, codes, table, table_index, work, opts)
{
  var bits = opts.bits;
      //here = opts.here; /* table entry for duplication */

  var len = 0;               /* a code's length in bits */
  var sym = 0;               /* index of code symbols */
  var min = 0, max = 0;          /* minimum and maximum code lengths */
  var root = 0;              /* number of index bits for root table */
  var curr = 0;              /* number of index bits for current table */
  var drop = 0;              /* code bits to drop for sub-table */
  var left = 0;                   /* number of prefix codes available */
  var used = 0;              /* code entries in table used */
  var huff = 0;              /* Huffman code */
  var incr;              /* for incrementing code, index */
  var fill;              /* index for replicating entries */
  var low;               /* low bits for current root entry */
  var mask;              /* mask for low root bits */
  var next;             /* next available space in table */
  var base = null;     /* base value table to use */
  var base_index = 0;
//  var shoextra;    /* extra bits table to use */
  var end;                    /* use base and extra for symbol > end */
  var count = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];    /* number of codes of each length */
  var offs = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];     /* offsets in table for each length */
  var extra = null;
  var extra_index = 0;

  var here_bits, here_op, here_val;

  /*
   Process a set of code lengths to create a canonical Huffman code.  The
   code lengths are lens[0..codes-1].  Each length corresponds to the
   symbols 0..codes-1.  The Huffman code is generated by first sorting the
   symbols by length from short to long, and retaining the symbol order
   for codes with equal lengths.  Then the code starts with all zero bits
   for the first code of the shortest length, and the codes are integer
   increments for the same length, and zeros are appended as the length
   increases.  For the deflate format, these bits are stored backwards
   from their more natural integer increment ordering, and so when the
   decoding tables are built in the large loop below, the integer codes
   are incremented backwards.

   This routine assumes, but does not check, that all of the entries in
   lens[] are in the range 0..MAXBITS.  The caller must assure this.
   1..MAXBITS is interpreted as that code length.  zero means that that
   symbol does not occur in this code.

   The codes are sorted by computing a count of codes for each length,
   creating from that a table of starting indices for each length in the
   sorted table, and then entering the symbols in order in the sorted
   table.  The sorted table is work[], with that space being provided by
   the caller.

   The length counts are used for other purposes as well, i.e. finding
   the minimum and maximum length codes, determining if there are any
   codes at all, checking for a valid set of lengths, and looking ahead
   at length counts to determine sub-table sizes when building the
   decoding tables.
   */

  /* accumulate lengths for codes (assumes lens[] all in 0..MAXBITS) */
  for (len = 0; len <= MAXBITS; len++) {
    count[len] = 0;
  }
  for (sym = 0; sym < codes; sym++) {
    count[lens[lens_index + sym]]++;
  }

  /* bound code lengths, force root to be within code lengths */
  root = bits;
  for (max = MAXBITS; max >= 1; max--) {
    if (count[max] !== 0) { break; }
  }
  if (root > max) {
    root = max;
  }
  if (max === 0) {                     /* no symbols to code at all */
    //table.op[opts.table_index] = 64;  //here.op = (var char)64;    /* invalid code marker */
    //table.bits[opts.table_index] = 1;   //here.bits = (var char)1;
    //table.val[opts.table_index++] = 0;   //here.val = (var short)0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;


    //table.op[opts.table_index] = 64;
    //table.bits[opts.table_index] = 1;
    //table.val[opts.table_index++] = 0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;

    opts.bits = 1;
    return 0;     /* no symbols, but wait for decoding to report error */
  }
  for (min = 1; min < max; min++) {
    if (count[min] !== 0) { break; }
  }
  if (root < min) {
    root = min;
  }

  /* check for an over-subscribed or incomplete set of lengths */
  left = 1;
  for (len = 1; len <= MAXBITS; len++) {
    left <<= 1;
    left -= count[len];
    if (left < 0) {
      return -1;
    }        /* over-subscribed */
  }
  if (left > 0 && (type === CODES || max !== 1)) {
    return -1;                      /* incomplete set */
  }

  /* generate offsets into symbol table for each length for sorting */
  offs[1] = 0;
  for (len = 1; len < MAXBITS; len++) {
    offs[len + 1] = offs[len] + count[len];
  }

  /* sort symbols by length, by symbol order within each length */
  for (sym = 0; sym < codes; sym++) {
    if (lens[lens_index + sym] !== 0) {
      work[offs[lens[lens_index + sym]]++] = sym;
    }
  }

  /*
   Create and fill in decoding tables.  In this loop, the table being
   filled is at next and has curr index bits.  The code being used is huff
   with length len.  That code is converted to an index by dropping drop
   bits off of the bottom.  For codes where len is less than drop + curr,
   those top drop + curr - len bits are incremented through all values to
   fill the table with replicated entries.

   root is the number of index bits for the root table.  When len exceeds
   root, sub-tables are created pointed to by the root entry with an index
   of the low root bits of huff.  This is saved in low to check for when a
   new sub-table should be started.  drop is zero when the root table is
   being filled, and drop is root when sub-tables are being filled.

   When a new sub-table is needed, it is necessary to look ahead in the
   code lengths to determine what size sub-table is needed.  The length
   counts are used for this, and so count[] is decremented as codes are
   entered in the tables.

   used keeps track of how many table entries have been allocated from the
   provided *table space.  It is checked for LENS and DIST tables against
   the constants ENOUGH_LENS and ENOUGH_DISTS to guard against changes in
   the initial root table size constants.  See the comments in inftrees.h
   for more information.

   sym increments through all symbols, and the loop terminates when
   all codes of length max, i.e. all codes, have been processed.  This
   routine permits incomplete codes, so another loop after this one fills
   in the rest of the decoding tables with invalid code markers.
   */

  /* set up for code type */
  // poor man optimization - use if-else instead of switch,
  // to avoid deopts in old v8
  if (type === CODES) {
    base = extra = work;    /* dummy value--not used */
    end = 19;

  } else if (type === LENS) {
    base = lbase;
    base_index -= 257;
    extra = lext;
    extra_index -= 257;
    end = 256;

  } else {                    /* DISTS */
    base = dbase;
    extra = dext;
    end = -1;
  }

  /* initialize opts for loop */
  huff = 0;                   /* starting code */
  sym = 0;                    /* starting code symbol */
  len = min;                  /* starting code length */
  next = table_index;              /* current table to fill in */
  curr = root;                /* current table index bits */
  drop = 0;                   /* current bits to drop from code for index */
  low = -1;                   /* trigger new sub-table when len > root */
  used = 1 << root;          /* use root table entries */
  mask = used - 1;            /* mask for comparing low */

  /* check available table space */
  if ((type === LENS && used > ENOUGH_LENS) ||
    (type === DISTS && used > ENOUGH_DISTS)) {
    return 1;
  }

  /* process all codes and make table entries */
  for (;;) {
    /* create table entry */
    here_bits = len - drop;
    if (work[sym] < end) {
      here_op = 0;
      here_val = work[sym];
    }
    else if (work[sym] > end) {
      here_op = extra[extra_index + work[sym]];
      here_val = base[base_index + work[sym]];
    }
    else {
      here_op = 32 + 64;         /* end of block */
      here_val = 0;
    }

    /* replicate for those indices with low len bits equal to huff */
    incr = 1 << (len - drop);
    fill = 1 << curr;
    min = fill;                 /* save offset to next table */
    do {
      fill -= incr;
      table[next + (huff >> drop) + fill] = (here_bits << 24) | (here_op << 16) | here_val |0;
    } while (fill !== 0);

    /* backwards increment the len-bit code huff */
    incr = 1 << (len - 1);
    while (huff & incr) {
      incr >>= 1;
    }
    if (incr !== 0) {
      huff &= incr - 1;
      huff += incr;
    } else {
      huff = 0;
    }

    /* go to next symbol, update count, len */
    sym++;
    if (--count[len] === 0) {
      if (len === max) { break; }
      len = lens[lens_index + work[sym]];
    }

    /* create new sub-table if needed */
    if (len > root && (huff & mask) !== low) {
      /* if first time, transition to sub-tables */
      if (drop === 0) {
        drop = root;
      }

      /* increment past last table */
      next += min;            /* here min is 1 << curr */

      /* determine length of next table */
      curr = len - drop;
      left = 1 << curr;
      while (curr + drop < max) {
        left -= count[curr + drop];
        if (left <= 0) { break; }
        curr++;
        left <<= 1;
      }

      /* check for enough space */
      used += 1 << curr;
      if ((type === LENS && used > ENOUGH_LENS) ||
        (type === DISTS && used > ENOUGH_DISTS)) {
        return 1;
      }

      /* point entry in root table to sub-table */
      low = huff & mask;
      /*table.op[low] = curr;
      table.bits[low] = root;
      table.val[low] = next - opts.table_index;*/
      table[low] = (root << 24) | (curr << 16) | (next - table_index) |0;
    }
  }

  /* fill in remaining table entry if code is incomplete (guaranteed to have
   at most one remaining entry, since if the code is incomplete, the
   maximum code length that was allowed to get this far is one bit) */
  if (huff !== 0) {
    //table.op[next + huff] = 64;            /* invalid code marker */
    //table.bits[next + huff] = len - drop;
    //table.val[next + huff] = 0;
    table[next + huff] = ((len - drop) << 24) | (64 << 16) |0;
  }

  /* set return parameters */
  //opts.table_index += used;
  opts.bits = root;
  return 0;
};


/***/ }),

/***/ 56228:
/***/ (function(module) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {
  2:      'need dictionary',     /* Z_NEED_DICT       2  */
  1:      'stream end',          /* Z_STREAM_END      1  */
  0:      '',                    /* Z_OK              0  */
  '-1':   'file error',          /* Z_ERRNO         (-1) */
  '-2':   'stream error',        /* Z_STREAM_ERROR  (-2) */
  '-3':   'data error',          /* Z_DATA_ERROR    (-3) */
  '-4':   'insufficient memory', /* Z_MEM_ERROR     (-4) */
  '-5':   'buffer error',        /* Z_BUF_ERROR     (-5) */
  '-6':   'incompatible version' /* Z_VERSION_ERROR (-6) */
};


/***/ }),

/***/ 22367:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

/* eslint-disable space-unary-ops */

var utils = __webpack_require__(72519);

/* Public constants ==========================================================*/
/* ===========================================================================*/


//var Z_FILTERED          = 1;
//var Z_HUFFMAN_ONLY      = 2;
//var Z_RLE               = 3;
var Z_FIXED               = 4;
//var Z_DEFAULT_STRATEGY  = 0;

/* Possible values of the data_type field (though see inflate()) */
var Z_BINARY              = 0;
var Z_TEXT                = 1;
//var Z_ASCII             = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;

/*============================================================================*/


function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }

// From zutil.h

var STORED_BLOCK = 0;
var STATIC_TREES = 1;
var DYN_TREES    = 2;
/* The three kinds of block type */

var MIN_MATCH    = 3;
var MAX_MATCH    = 258;
/* The minimum and maximum match lengths */

// From deflate.h
/* ===========================================================================
 * Internal compression state.
 */

var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */

var LITERALS      = 256;
/* number of literal bytes 0..255 */

var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */

var D_CODES       = 30;
/* number of distance codes */

var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */

var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */

var MAX_BITS      = 15;
/* All codes must not exceed MAX_BITS bits */

var Buf_size      = 16;
/* size of bit buffer in bi_buf */


/* ===========================================================================
 * Constants
 */

var MAX_BL_BITS = 7;
/* Bit length codes must not exceed MAX_BL_BITS bits */

var END_BLOCK   = 256;
/* end of block literal code */

var REP_3_6     = 16;
/* repeat previous bit length 3-6 times (2 bits of repeat count) */

var REPZ_3_10   = 17;
/* repeat a zero length 3-10 times  (3 bits of repeat count) */

var REPZ_11_138 = 18;
/* repeat a zero length 11-138 times  (7 bits of repeat count) */

/* eslint-disable comma-spacing,array-bracket-spacing */
var extra_lbits =   /* extra bits for each length code */
  [0,0,0,0,0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,0];

var extra_dbits =   /* extra bits for each distance code */
  [0,0,0,0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,12,12,13,13];

var extra_blbits =  /* extra bits for each bit length code */
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,2,3,7];

var bl_order =
  [16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15];
/* eslint-enable comma-spacing,array-bracket-spacing */

/* The lengths of the bit length codes are sent in order of decreasing
 * probability, to avoid transmitting the lengths for unused bit length codes.
 */

/* ===========================================================================
 * Local data. These are initialized only once.
 */

// We pre-fill arrays with 0 to avoid uninitialized gaps

var DIST_CODE_LEN = 512; /* see definition of array dist_code below */

// !!!! Use flat array instead of structure, Freq = i*2, Len = i*2+1
var static_ltree  = new Array((L_CODES + 2) * 2);
zero(static_ltree);
/* The static literal tree. Since the bit lengths are imposed, there is no
 * need for the L_CODES extra codes used during heap construction. However
 * The codes 286 and 287 are needed to build a canonical tree (see _tr_init
 * below).
 */

var static_dtree  = new Array(D_CODES * 2);
zero(static_dtree);
/* The static distance tree. (Actually a trivial tree since all codes use
 * 5 bits.)
 */

var _dist_code    = new Array(DIST_CODE_LEN);
zero(_dist_code);
/* Distance codes. The first 256 values correspond to the distances
 * 3 .. 258, the last 256 values correspond to the top 8 bits of
 * the 15 bit distances.
 */

var _length_code  = new Array(MAX_MATCH - MIN_MATCH + 1);
zero(_length_code);
/* length code for each normalized match length (0 == MIN_MATCH) */

var base_length   = new Array(LENGTH_CODES);
zero(base_length);
/* First normalized length for each code (0 = MIN_MATCH) */

var base_dist     = new Array(D_CODES);
zero(base_dist);
/* First normalized distance for each code (0 = distance of 1) */


function StaticTreeDesc(static_tree, extra_bits, extra_base, elems, max_length) {

  this.static_tree  = static_tree;  /* static tree or NULL */
  this.extra_bits   = extra_bits;   /* extra bits for each code or NULL */
  this.extra_base   = extra_base;   /* base index for extra_bits */
  this.elems        = elems;        /* max number of elements in the tree */
  this.max_length   = max_length;   /* max bit length for the codes */

  // show if `static_tree` has data or dummy - needed for monomorphic objects
  this.has_stree    = static_tree && static_tree.length;
}


var static_l_desc;
var static_d_desc;
var static_bl_desc;


function TreeDesc(dyn_tree, stat_desc) {
  this.dyn_tree = dyn_tree;     /* the dynamic tree */
  this.max_code = 0;            /* largest code with non zero frequency */
  this.stat_desc = stat_desc;   /* the corresponding static tree */
}



function d_code(dist) {
  return dist < 256 ? _dist_code[dist] : _dist_code[256 + (dist >>> 7)];
}


/* ===========================================================================
 * Output a short LSB first on the stream.
 * IN assertion: there is enough room in pendingBuf.
 */
function put_short(s, w) {
//    put_byte(s, (uch)((w) & 0xff));
//    put_byte(s, (uch)((ush)(w) >> 8));
  s.pending_buf[s.pending++] = (w) & 0xff;
  s.pending_buf[s.pending++] = (w >>> 8) & 0xff;
}


/* ===========================================================================
 * Send a value on a given number of bits.
 * IN assertion: length <= 16 and value fits in length bits.
 */
function send_bits(s, value, length) {
  if (s.bi_valid > (Buf_size - length)) {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    put_short(s, s.bi_buf);
    s.bi_buf = value >> (Buf_size - s.bi_valid);
    s.bi_valid += length - Buf_size;
  } else {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    s.bi_valid += length;
  }
}


function send_code(s, c, tree) {
  send_bits(s, tree[c * 2]/*.Code*/, tree[c * 2 + 1]/*.Len*/);
}


/* ===========================================================================
 * Reverse the first len bits of a code, using straightforward code (a faster
 * method would use a table)
 * IN assertion: 1 <= len <= 15
 */
function bi_reverse(code, len) {
  var res = 0;
  do {
    res |= code & 1;
    code >>>= 1;
    res <<= 1;
  } while (--len > 0);
  return res >>> 1;
}


/* ===========================================================================
 * Flush the bit buffer, keeping at most 7 bits in it.
 */
function bi_flush(s) {
  if (s.bi_valid === 16) {
    put_short(s, s.bi_buf);
    s.bi_buf = 0;
    s.bi_valid = 0;

  } else if (s.bi_valid >= 8) {
    s.pending_buf[s.pending++] = s.bi_buf & 0xff;
    s.bi_buf >>= 8;
    s.bi_valid -= 8;
  }
}


/* ===========================================================================
 * Compute the optimal bit lengths for a tree and update the total bit length
 * for the current block.
 * IN assertion: the fields freq and dad are set, heap[heap_max] and
 *    above are the tree nodes sorted by increasing frequency.
 * OUT assertions: the field len is set to the optimal bit length, the
 *     array bl_count contains the frequencies for each bit length.
 *     The length opt_len is updated; static_len is also updated if stree is
 *     not null.
 */
function gen_bitlen(s, desc)
//    deflate_state *s;
//    tree_desc *desc;    /* the tree descriptor */
{
  var tree            = desc.dyn_tree;
  var max_code        = desc.max_code;
  var stree           = desc.stat_desc.static_tree;
  var has_stree       = desc.stat_desc.has_stree;
  var extra           = desc.stat_desc.extra_bits;
  var base            = desc.stat_desc.extra_base;
  var max_length      = desc.stat_desc.max_length;
  var h;              /* heap index */
  var n, m;           /* iterate over the tree elements */
  var bits;           /* bit length */
  var xbits;          /* extra bits */
  var f;              /* frequency */
  var overflow = 0;   /* number of elements with bit length too large */

  for (bits = 0; bits <= MAX_BITS; bits++) {
    s.bl_count[bits] = 0;
  }

  /* In a first pass, compute the optimal bit lengths (which may
   * overflow in the case of the bit length tree).
   */
  tree[s.heap[s.heap_max] * 2 + 1]/*.Len*/ = 0; /* root of the heap */

  for (h = s.heap_max + 1; h < HEAP_SIZE; h++) {
    n = s.heap[h];
    bits = tree[tree[n * 2 + 1]/*.Dad*/ * 2 + 1]/*.Len*/ + 1;
    if (bits > max_length) {
      bits = max_length;
      overflow++;
    }
    tree[n * 2 + 1]/*.Len*/ = bits;
    /* We overwrite tree[n].Dad which is no longer needed */

    if (n > max_code) { continue; } /* not a leaf node */

    s.bl_count[bits]++;
    xbits = 0;
    if (n >= base) {
      xbits = extra[n - base];
    }
    f = tree[n * 2]/*.Freq*/;
    s.opt_len += f * (bits + xbits);
    if (has_stree) {
      s.static_len += f * (stree[n * 2 + 1]/*.Len*/ + xbits);
    }
  }
  if (overflow === 0) { return; }

  // Trace((stderr,"\nbit length overflow\n"));
  /* This happens for example on obj2 and pic of the Calgary corpus */

  /* Find the first bit length which could increase: */
  do {
    bits = max_length - 1;
    while (s.bl_count[bits] === 0) { bits--; }
    s.bl_count[bits]--;      /* move one leaf down the tree */
    s.bl_count[bits + 1] += 2; /* move one overflow item as its brother */
    s.bl_count[max_length]--;
    /* The brother of the overflow item also moves one step up,
     * but this does not affect bl_count[max_length]
     */
    overflow -= 2;
  } while (overflow > 0);

  /* Now recompute all bit lengths, scanning in increasing frequency.
   * h is still equal to HEAP_SIZE. (It is simpler to reconstruct all
   * lengths instead of fixing only the wrong ones. This idea is taken
   * from 'ar' written by Haruhiko Okumura.)
   */
  for (bits = max_length; bits !== 0; bits--) {
    n = s.bl_count[bits];
    while (n !== 0) {
      m = s.heap[--h];
      if (m > max_code) { continue; }
      if (tree[m * 2 + 1]/*.Len*/ !== bits) {
        // Trace((stderr,"code %d bits %d->%d\n", m, tree[m].Len, bits));
        s.opt_len += (bits - tree[m * 2 + 1]/*.Len*/) * tree[m * 2]/*.Freq*/;
        tree[m * 2 + 1]/*.Len*/ = bits;
      }
      n--;
    }
  }
}


/* ===========================================================================
 * Generate the codes for a given tree and bit counts (which need not be
 * optimal).
 * IN assertion: the array bl_count contains the bit length statistics for
 * the given tree and the field len is set for all tree elements.
 * OUT assertion: the field code is set for all tree elements of non
 *     zero code length.
 */
function gen_codes(tree, max_code, bl_count)
//    ct_data *tree;             /* the tree to decorate */
//    int max_code;              /* largest code with non zero frequency */
//    ushf *bl_count;            /* number of codes at each bit length */
{
  var next_code = new Array(MAX_BITS + 1); /* next code value for each bit length */
  var code = 0;              /* running code value */
  var bits;                  /* bit index */
  var n;                     /* code index */

  /* The distribution counts are first used to generate the code values
   * without bit reversal.
   */
  for (bits = 1; bits <= MAX_BITS; bits++) {
    next_code[bits] = code = (code + bl_count[bits - 1]) << 1;
  }
  /* Check that the bit counts in bl_count are consistent. The last code
   * must be all ones.
   */
  //Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
  //        "inconsistent bit counts");
  //Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

  for (n = 0;  n <= max_code; n++) {
    var len = tree[n * 2 + 1]/*.Len*/;
    if (len === 0) { continue; }
    /* Now reverse the bits */
    tree[n * 2]/*.Code*/ = bi_reverse(next_code[len]++, len);

    //Tracecv(tree != static_ltree, (stderr,"\nn %3d %c l %2d c %4x (%x) ",
    //     n, (isgraph(n) ? n : ' '), len, tree[n].Code, next_code[len]-1));
  }
}


/* ===========================================================================
 * Initialize the various 'constant' tables.
 */
function tr_static_init() {
  var n;        /* iterates over tree elements */
  var bits;     /* bit counter */
  var length;   /* length value */
  var code;     /* code value */
  var dist;     /* distance index */
  var bl_count = new Array(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  // do check in _tr_init()
  //if (static_init_done) return;

  /* For some embedded targets, global variables are not initialized: */
/*#ifdef NO_INIT_GLOBAL_POINTERS
  static_l_desc.static_tree = static_ltree;
  static_l_desc.extra_bits = extra_lbits;
  static_d_desc.static_tree = static_dtree;
  static_d_desc.extra_bits = extra_dbits;
  static_bl_desc.extra_bits = extra_blbits;
#endif*/

  /* Initialize the mapping length (0..255) -> length code (0..28) */
  length = 0;
  for (code = 0; code < LENGTH_CODES - 1; code++) {
    base_length[code] = length;
    for (n = 0; n < (1 << extra_lbits[code]); n++) {
      _length_code[length++] = code;
    }
  }
  //Assert (length == 256, "tr_static_init: length != 256");
  /* Note that the length 255 (match length 258) can be represented
   * in two different ways: code 284 + 5 bits or code 285, so we
   * overwrite length_code[255] to use the best encoding:
   */
  _length_code[length - 1] = code;

  /* Initialize the mapping dist (0..32K) -> dist code (0..29) */
  dist = 0;
  for (code = 0; code < 16; code++) {
    base_dist[code] = dist;
    for (n = 0; n < (1 << extra_dbits[code]); n++) {
      _dist_code[dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: dist != 256");
  dist >>= 7; /* from now on, all distances are divided by 128 */
  for (; code < D_CODES; code++) {
    base_dist[code] = dist << 7;
    for (n = 0; n < (1 << (extra_dbits[code] - 7)); n++) {
      _dist_code[256 + dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: 256+dist != 512");

  /* Construct the codes of the static literal tree */
  for (bits = 0; bits <= MAX_BITS; bits++) {
    bl_count[bits] = 0;
  }

  n = 0;
  while (n <= 143) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  while (n <= 255) {
    static_ltree[n * 2 + 1]/*.Len*/ = 9;
    n++;
    bl_count[9]++;
  }
  while (n <= 279) {
    static_ltree[n * 2 + 1]/*.Len*/ = 7;
    n++;
    bl_count[7]++;
  }
  while (n <= 287) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  /* Codes 286 and 287 do not exist, but we must include them in the
   * tree construction to get a canonical Huffman tree (longest code
   * all ones)
   */
  gen_codes(static_ltree, L_CODES + 1, bl_count);

  /* The static distance tree is trivial: */
  for (n = 0; n < D_CODES; n++) {
    static_dtree[n * 2 + 1]/*.Len*/ = 5;
    static_dtree[n * 2]/*.Code*/ = bi_reverse(n, 5);
  }

  // Now data ready and we can init static trees
  static_l_desc = new StaticTreeDesc(static_ltree, extra_lbits, LITERALS + 1, L_CODES, MAX_BITS);
  static_d_desc = new StaticTreeDesc(static_dtree, extra_dbits, 0,          D_CODES, MAX_BITS);
  static_bl_desc = new StaticTreeDesc(new Array(0), extra_blbits, 0,         BL_CODES, MAX_BL_BITS);

  //static_init_done = true;
}


/* ===========================================================================
 * Initialize a new block.
 */
function init_block(s) {
  var n; /* iterates over tree elements */

  /* Initialize the trees. */
  for (n = 0; n < L_CODES;  n++) { s.dyn_ltree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < D_CODES;  n++) { s.dyn_dtree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < BL_CODES; n++) { s.bl_tree[n * 2]/*.Freq*/ = 0; }

  s.dyn_ltree[END_BLOCK * 2]/*.Freq*/ = 1;
  s.opt_len = s.static_len = 0;
  s.last_lit = s.matches = 0;
}


/* ===========================================================================
 * Flush the bit buffer and align the output on a byte boundary
 */
function bi_windup(s)
{
  if (s.bi_valid > 8) {
    put_short(s, s.bi_buf);
  } else if (s.bi_valid > 0) {
    //put_byte(s, (Byte)s->bi_buf);
    s.pending_buf[s.pending++] = s.bi_buf;
  }
  s.bi_buf = 0;
  s.bi_valid = 0;
}

/* ===========================================================================
 * Copy a stored block, storing first the length and its
 * one's complement if requested.
 */
function copy_block(s, buf, len, header)
//DeflateState *s;
//charf    *buf;    /* the input data */
//unsigned len;     /* its length */
//int      header;  /* true if block header must be written */
{
  bi_windup(s);        /* align on byte boundary */

  if (header) {
    put_short(s, len);
    put_short(s, ~len);
  }
//  while (len--) {
//    put_byte(s, *buf++);
//  }
  utils.arraySet(s.pending_buf, s.window, buf, len, s.pending);
  s.pending += len;
}

/* ===========================================================================
 * Compares to subtrees, using the tree depth as tie breaker when
 * the subtrees have equal frequency. This minimizes the worst case length.
 */
function smaller(tree, n, m, depth) {
  var _n2 = n * 2;
  var _m2 = m * 2;
  return (tree[_n2]/*.Freq*/ < tree[_m2]/*.Freq*/ ||
         (tree[_n2]/*.Freq*/ === tree[_m2]/*.Freq*/ && depth[n] <= depth[m]));
}

/* ===========================================================================
 * Restore the heap property by moving down the tree starting at node k,
 * exchanging a node with the smallest of its two sons if necessary, stopping
 * when the heap property is re-established (each father smaller than its
 * two sons).
 */
function pqdownheap(s, tree, k)
//    deflate_state *s;
//    ct_data *tree;  /* the tree to restore */
//    int k;               /* node to move down */
{
  var v = s.heap[k];
  var j = k << 1;  /* left son of k */
  while (j <= s.heap_len) {
    /* Set j to the smallest of the two sons: */
    if (j < s.heap_len &&
      smaller(tree, s.heap[j + 1], s.heap[j], s.depth)) {
      j++;
    }
    /* Exit if v is smaller than both sons */
    if (smaller(tree, v, s.heap[j], s.depth)) { break; }

    /* Exchange v with the smallest son */
    s.heap[k] = s.heap[j];
    k = j;

    /* And continue down the tree, setting j to the left son of k */
    j <<= 1;
  }
  s.heap[k] = v;
}


// inlined manually
// var SMALLEST = 1;

/* ===========================================================================
 * Send the block data compressed using the given Huffman trees
 */
function compress_block(s, ltree, dtree)
//    deflate_state *s;
//    const ct_data *ltree; /* literal tree */
//    const ct_data *dtree; /* distance tree */
{
  var dist;           /* distance of matched string */
  var lc;             /* match length or unmatched char (if dist == 0) */
  var lx = 0;         /* running index in l_buf */
  var code;           /* the code to send */
  var extra;          /* number of extra bits to send */

  if (s.last_lit !== 0) {
    do {
      dist = (s.pending_buf[s.d_buf + lx * 2] << 8) | (s.pending_buf[s.d_buf + lx * 2 + 1]);
      lc = s.pending_buf[s.l_buf + lx];
      lx++;

      if (dist === 0) {
        send_code(s, lc, ltree); /* send a literal byte */
        //Tracecv(isgraph(lc), (stderr," '%c' ", lc));
      } else {
        /* Here, lc is the match length - MIN_MATCH */
        code = _length_code[lc];
        send_code(s, code + LITERALS + 1, ltree); /* send the length code */
        extra = extra_lbits[code];
        if (extra !== 0) {
          lc -= base_length[code];
          send_bits(s, lc, extra);       /* send the extra length bits */
        }
        dist--; /* dist is now the match distance - 1 */
        code = d_code(dist);
        //Assert (code < D_CODES, "bad d_code");

        send_code(s, code, dtree);       /* send the distance code */
        extra = extra_dbits[code];
        if (extra !== 0) {
          dist -= base_dist[code];
          send_bits(s, dist, extra);   /* send the extra distance bits */
        }
      } /* literal or match pair ? */

      /* Check that the overlay between pending_buf and d_buf+l_buf is ok: */
      //Assert((uInt)(s->pending) < s->lit_bufsize + 2*lx,
      //       "pendingBuf overflow");

    } while (lx < s.last_lit);
  }

  send_code(s, END_BLOCK, ltree);
}


/* ===========================================================================
 * Construct one Huffman tree and assigns the code bit strings and lengths.
 * Update the total bit length for the current block.
 * IN assertion: the field freq is set for all tree elements.
 * OUT assertions: the fields len and code are set to the optimal bit length
 *     and corresponding code. The length opt_len is updated; static_len is
 *     also updated if stree is not null. The field max_code is set.
 */
function build_tree(s, desc)
//    deflate_state *s;
//    tree_desc *desc; /* the tree descriptor */
{
  var tree     = desc.dyn_tree;
  var stree    = desc.stat_desc.static_tree;
  var has_stree = desc.stat_desc.has_stree;
  var elems    = desc.stat_desc.elems;
  var n, m;          /* iterate over heap elements */
  var max_code = -1; /* largest code with non zero frequency */
  var node;          /* new node being created */

  /* Construct the initial heap, with least frequent element in
   * heap[SMALLEST]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
   * heap[0] is not used.
   */
  s.heap_len = 0;
  s.heap_max = HEAP_SIZE;

  for (n = 0; n < elems; n++) {
    if (tree[n * 2]/*.Freq*/ !== 0) {
      s.heap[++s.heap_len] = max_code = n;
      s.depth[n] = 0;

    } else {
      tree[n * 2 + 1]/*.Len*/ = 0;
    }
  }

  /* The pkzip format requires that at least one distance code exists,
   * and that at least one bit should be sent even if there is only one
   * possible code. So to avoid special checks later on we force at least
   * two codes of non zero frequency.
   */
  while (s.heap_len < 2) {
    node = s.heap[++s.heap_len] = (max_code < 2 ? ++max_code : 0);
    tree[node * 2]/*.Freq*/ = 1;
    s.depth[node] = 0;
    s.opt_len--;

    if (has_stree) {
      s.static_len -= stree[node * 2 + 1]/*.Len*/;
    }
    /* node is 0 or 1 so it does not have extra bits */
  }
  desc.max_code = max_code;

  /* The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
   * establish sub-heaps of increasing lengths:
   */
  for (n = (s.heap_len >> 1/*int /2*/); n >= 1; n--) { pqdownheap(s, tree, n); }

  /* Construct the Huffman tree by repeatedly combining the least two
   * frequent nodes.
   */
  node = elems;              /* next internal node of the tree */
  do {
    //pqremove(s, tree, n);  /* n = node of least frequency */
    /*** pqremove ***/
    n = s.heap[1/*SMALLEST*/];
    s.heap[1/*SMALLEST*/] = s.heap[s.heap_len--];
    pqdownheap(s, tree, 1/*SMALLEST*/);
    /***/

    m = s.heap[1/*SMALLEST*/]; /* m = node of next least frequency */

    s.heap[--s.heap_max] = n; /* keep the nodes sorted by frequency */
    s.heap[--s.heap_max] = m;

    /* Create a new node father of n and m */
    tree[node * 2]/*.Freq*/ = tree[n * 2]/*.Freq*/ + tree[m * 2]/*.Freq*/;
    s.depth[node] = (s.depth[n] >= s.depth[m] ? s.depth[n] : s.depth[m]) + 1;
    tree[n * 2 + 1]/*.Dad*/ = tree[m * 2 + 1]/*.Dad*/ = node;

    /* and insert the new node in the heap */
    s.heap[1/*SMALLEST*/] = node++;
    pqdownheap(s, tree, 1/*SMALLEST*/);

  } while (s.heap_len >= 2);

  s.heap[--s.heap_max] = s.heap[1/*SMALLEST*/];

  /* At this point, the fields freq and dad are set. We can now
   * generate the bit lengths.
   */
  gen_bitlen(s, desc);

  /* The field len is now set, we can generate the bit codes */
  gen_codes(tree, max_code, s.bl_count);
}


/* ===========================================================================
 * Scan a literal or distance tree to determine the frequencies of the codes
 * in the bit length tree.
 */
function scan_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree;   /* the tree to be scanned */
//    int max_code;    /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }
  tree[(max_code + 1) * 2 + 1]/*.Len*/ = 0xffff; /* guard */

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      s.bl_tree[curlen * 2]/*.Freq*/ += count;

    } else if (curlen !== 0) {

      if (curlen !== prevlen) { s.bl_tree[curlen * 2]/*.Freq*/++; }
      s.bl_tree[REP_3_6 * 2]/*.Freq*/++;

    } else if (count <= 10) {
      s.bl_tree[REPZ_3_10 * 2]/*.Freq*/++;

    } else {
      s.bl_tree[REPZ_11_138 * 2]/*.Freq*/++;
    }

    count = 0;
    prevlen = curlen;

    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Send a literal or distance tree in compressed form, using the codes in
 * bl_tree.
 */
function send_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree; /* the tree to be scanned */
//    int max_code;       /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  /* tree[max_code+1].Len = -1; */  /* guard already set */
  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      do { send_code(s, curlen, s.bl_tree); } while (--count !== 0);

    } else if (curlen !== 0) {
      if (curlen !== prevlen) {
        send_code(s, curlen, s.bl_tree);
        count--;
      }
      //Assert(count >= 3 && count <= 6, " 3_6?");
      send_code(s, REP_3_6, s.bl_tree);
      send_bits(s, count - 3, 2);

    } else if (count <= 10) {
      send_code(s, REPZ_3_10, s.bl_tree);
      send_bits(s, count - 3, 3);

    } else {
      send_code(s, REPZ_11_138, s.bl_tree);
      send_bits(s, count - 11, 7);
    }

    count = 0;
    prevlen = curlen;
    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Construct the Huffman tree for the bit lengths and return the index in
 * bl_order of the last bit length code to send.
 */
function build_bl_tree(s) {
  var max_blindex;  /* index of last bit length code of non zero freq */

  /* Determine the bit length frequencies for literal and distance trees */
  scan_tree(s, s.dyn_ltree, s.l_desc.max_code);
  scan_tree(s, s.dyn_dtree, s.d_desc.max_code);

  /* Build the bit length tree: */
  build_tree(s, s.bl_desc);
  /* opt_len now includes the length of the tree representations, except
   * the lengths of the bit lengths codes and the 5+5+4 bits for the counts.
   */

  /* Determine the number of bit length codes to send. The pkzip format
   * requires that at least 4 bit length codes be sent. (appnote.txt says
   * 3 but the actual value used is 4.)
   */
  for (max_blindex = BL_CODES - 1; max_blindex >= 3; max_blindex--) {
    if (s.bl_tree[bl_order[max_blindex] * 2 + 1]/*.Len*/ !== 0) {
      break;
    }
  }
  /* Update opt_len to include the bit length tree and counts */
  s.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;
  //Tracev((stderr, "\ndyn trees: dyn %ld, stat %ld",
  //        s->opt_len, s->static_len));

  return max_blindex;
}


/* ===========================================================================
 * Send the header for a block using dynamic Huffman trees: the counts, the
 * lengths of the bit length codes, the literal tree and the distance tree.
 * IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
 */
function send_all_trees(s, lcodes, dcodes, blcodes)
//    deflate_state *s;
//    int lcodes, dcodes, blcodes; /* number of codes for each tree */
{
  var rank;                    /* index in bl_order */

  //Assert (lcodes >= 257 && dcodes >= 1 && blcodes >= 4, "not enough codes");
  //Assert (lcodes <= L_CODES && dcodes <= D_CODES && blcodes <= BL_CODES,
  //        "too many codes");
  //Tracev((stderr, "\nbl counts: "));
  send_bits(s, lcodes - 257, 5); /* not +255 as stated in appnote.txt */
  send_bits(s, dcodes - 1,   5);
  send_bits(s, blcodes - 4,  4); /* not -3 as stated in appnote.txt */
  for (rank = 0; rank < blcodes; rank++) {
    //Tracev((stderr, "\nbl code %2d ", bl_order[rank]));
    send_bits(s, s.bl_tree[bl_order[rank] * 2 + 1]/*.Len*/, 3);
  }
  //Tracev((stderr, "\nbl tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_ltree, lcodes - 1); /* literal tree */
  //Tracev((stderr, "\nlit tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_dtree, dcodes - 1); /* distance tree */
  //Tracev((stderr, "\ndist tree: sent %ld", s->bits_sent));
}


/* ===========================================================================
 * Check if the data type is TEXT or BINARY, using the following algorithm:
 * - TEXT if the two conditions below are satisfied:
 *    a) There are no non-portable control characters belonging to the
 *       "black list" (0..6, 14..25, 28..31).
 *    b) There is at least one printable character belonging to the
 *       "white list" (9 {TAB}, 10 {LF}, 13 {CR}, 32..255).
 * - BINARY otherwise.
 * - The following partially-portable control characters form a
 *   "gray list" that is ignored in this detection algorithm:
 *   (7 {BEL}, 8 {BS}, 11 {VT}, 12 {FF}, 26 {SUB}, 27 {ESC}).
 * IN assertion: the fields Freq of dyn_ltree are set.
 */
function detect_data_type(s) {
  /* black_mask is the bit mask of black-listed bytes
   * set bits 0..6, 14..25, and 28..31
   * 0xf3ffc07f = binary 11110011111111111100000001111111
   */
  var black_mask = 0xf3ffc07f;
  var n;

  /* Check for non-textual ("black-listed") bytes. */
  for (n = 0; n <= 31; n++, black_mask >>>= 1) {
    if ((black_mask & 1) && (s.dyn_ltree[n * 2]/*.Freq*/ !== 0)) {
      return Z_BINARY;
    }
  }

  /* Check for textual ("white-listed") bytes. */
  if (s.dyn_ltree[9 * 2]/*.Freq*/ !== 0 || s.dyn_ltree[10 * 2]/*.Freq*/ !== 0 ||
      s.dyn_ltree[13 * 2]/*.Freq*/ !== 0) {
    return Z_TEXT;
  }
  for (n = 32; n < LITERALS; n++) {
    if (s.dyn_ltree[n * 2]/*.Freq*/ !== 0) {
      return Z_TEXT;
    }
  }

  /* There are no "black-listed" or "white-listed" bytes:
   * this stream either is empty or has tolerated ("gray-listed") bytes only.
   */
  return Z_BINARY;
}


var static_init_done = false;

/* ===========================================================================
 * Initialize the tree data structures for a new zlib stream.
 */
function _tr_init(s)
{

  if (!static_init_done) {
    tr_static_init();
    static_init_done = true;
  }

  s.l_desc  = new TreeDesc(s.dyn_ltree, static_l_desc);
  s.d_desc  = new TreeDesc(s.dyn_dtree, static_d_desc);
  s.bl_desc = new TreeDesc(s.bl_tree, static_bl_desc);

  s.bi_buf = 0;
  s.bi_valid = 0;

  /* Initialize the first block of the first file: */
  init_block(s);
}


/* ===========================================================================
 * Send a stored block
 */
function _tr_stored_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  send_bits(s, (STORED_BLOCK << 1) + (last ? 1 : 0), 3);    /* send block type */
  copy_block(s, buf, stored_len, true); /* with header */
}


/* ===========================================================================
 * Send one empty static block to give enough lookahead for inflate.
 * This takes 10 bits, of which 7 may remain in the bit buffer.
 */
function _tr_align(s) {
  send_bits(s, STATIC_TREES << 1, 3);
  send_code(s, END_BLOCK, static_ltree);
  bi_flush(s);
}


/* ===========================================================================
 * Determine the best encoding for the current block: dynamic trees, static
 * trees or store, and output the encoded block to the zip file.
 */
function _tr_flush_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block, or NULL if too old */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  var opt_lenb, static_lenb;  /* opt_len and static_len in bytes */
  var max_blindex = 0;        /* index of last bit length code of non zero freq */

  /* Build the Huffman trees unless a stored block is forced */
  if (s.level > 0) {

    /* Check if the file is binary or text */
    if (s.strm.data_type === Z_UNKNOWN) {
      s.strm.data_type = detect_data_type(s);
    }

    /* Construct the literal and distance trees */
    build_tree(s, s.l_desc);
    // Tracev((stderr, "\nlit data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));

    build_tree(s, s.d_desc);
    // Tracev((stderr, "\ndist data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));
    /* At this point, opt_len and static_len are the total bit lengths of
     * the compressed block data, excluding the tree representations.
     */

    /* Build the bit length tree for the above two trees, and get the index
     * in bl_order of the last bit length code to send.
     */
    max_blindex = build_bl_tree(s);

    /* Determine the best encoding. Compute the block lengths in bytes. */
    opt_lenb = (s.opt_len + 3 + 7) >>> 3;
    static_lenb = (s.static_len + 3 + 7) >>> 3;

    // Tracev((stderr, "\nopt %lu(%lu) stat %lu(%lu) stored %lu lit %u ",
    //        opt_lenb, s->opt_len, static_lenb, s->static_len, stored_len,
    //        s->last_lit));

    if (static_lenb <= opt_lenb) { opt_lenb = static_lenb; }

  } else {
    // Assert(buf != (char*)0, "lost buf");
    opt_lenb = static_lenb = stored_len + 5; /* force a stored block */
  }

  if ((stored_len + 4 <= opt_lenb) && (buf !== -1)) {
    /* 4: two words for the lengths */

    /* The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
     * Otherwise we can't have processed more than WSIZE input bytes since
     * the last block flush, because compression would have been
     * successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
     * transform a block into a stored block.
     */
    _tr_stored_block(s, buf, stored_len, last);

  } else if (s.strategy === Z_FIXED || static_lenb === opt_lenb) {

    send_bits(s, (STATIC_TREES << 1) + (last ? 1 : 0), 3);
    compress_block(s, static_ltree, static_dtree);

  } else {
    send_bits(s, (DYN_TREES << 1) + (last ? 1 : 0), 3);
    send_all_trees(s, s.l_desc.max_code + 1, s.d_desc.max_code + 1, max_blindex + 1);
    compress_block(s, s.dyn_ltree, s.dyn_dtree);
  }
  // Assert (s->compressed_len == s->bits_sent, "bad compressed size");
  /* The above check is made mod 2^32, for files larger than 512 MB
   * and uLong implemented on 32 bits.
   */
  init_block(s);

  if (last) {
    bi_windup(s);
  }
  // Tracev((stderr,"\ncomprlen %lu(%lu) ", s->compressed_len>>3,
  //       s->compressed_len-7*last));
}

/* ===========================================================================
 * Save the match info and tally the frequency counts. Return true if
 * the current block must be flushed.
 */
function _tr_tally(s, dist, lc)
//    deflate_state *s;
//    unsigned dist;  /* distance of matched string */
//    unsigned lc;    /* match length-MIN_MATCH or unmatched char (if dist==0) */
{
  //var out_length, in_length, dcode;

  s.pending_buf[s.d_buf + s.last_lit * 2]     = (dist >>> 8) & 0xff;
  s.pending_buf[s.d_buf + s.last_lit * 2 + 1] = dist & 0xff;

  s.pending_buf[s.l_buf + s.last_lit] = lc & 0xff;
  s.last_lit++;

  if (dist === 0) {
    /* lc is the unmatched char */
    s.dyn_ltree[lc * 2]/*.Freq*/++;
  } else {
    s.matches++;
    /* Here, lc is the match length - MIN_MATCH */
    dist--;             /* dist = match distance - 1 */
    //Assert((ush)dist < (ush)MAX_DIST(s) &&
    //       (ush)lc <= (ush)(MAX_MATCH-MIN_MATCH) &&
    //       (ush)d_code(dist) < (ush)D_CODES,  "_tr_tally: bad match");

    s.dyn_ltree[(_length_code[lc] + LITERALS + 1) * 2]/*.Freq*/++;
    s.dyn_dtree[d_code(dist) * 2]/*.Freq*/++;
  }

// (!) This block is disabled in zlib defaults,
// don't enable it for binary compatibility

//#ifdef TRUNCATE_BLOCK
//  /* Try to guess if it is profitable to stop the current block here */
//  if ((s.last_lit & 0x1fff) === 0 && s.level > 2) {
//    /* Compute an upper bound for the compressed length */
//    out_length = s.last_lit*8;
//    in_length = s.strstart - s.block_start;
//
//    for (dcode = 0; dcode < D_CODES; dcode++) {
//      out_length += s.dyn_dtree[dcode*2]/*.Freq*/ * (5 + extra_dbits[dcode]);
//    }
//    out_length >>>= 3;
//    //Tracev((stderr,"\nlast_lit %u, in %ld, out ~%ld(%ld%%) ",
//    //       s->last_lit, in_length, out_length,
//    //       100L - out_length*100L/in_length));
//    if (s.matches < (s.last_lit>>1)/*int /2*/ && out_length < (in_length>>1)/*int /2*/) {
//      return true;
//    }
//  }
//#endif

  return (s.last_lit === s.lit_bufsize - 1);
  /* We avoid equality with lit_bufsize because of wraparound at 64K
   * on 16 bit machines and because stored blocks are restricted to
   * 64K-1 bytes.
   */
}

exports._tr_init  = _tr_init;
exports._tr_stored_block = _tr_stored_block;
exports._tr_flush_block  = _tr_flush_block;
exports._tr_tally = _tr_tally;
exports._tr_align = _tr_align;


/***/ }),

/***/ 37468:
/***/ (function(module) {

"use strict";


// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function ZStream() {
  /* next input byte */
  this.input = null; // JS specific, because we have no pointers
  this.next_in = 0;
  /* number of bytes available at input */
  this.avail_in = 0;
  /* total number of input bytes read so far */
  this.total_in = 0;
  /* next output byte should be put there */
  this.output = null; // JS specific, because we have no pointers
  this.next_out = 0;
  /* remaining free space at output */
  this.avail_out = 0;
  /* total number of bytes output so far */
  this.total_out = 0;
  /* last error message, NULL if no error */
  this.msg = ''/*Z_NULL*/;
  /* not visible by applications */
  this.state = null;
  /* best guess about the data type: binary or text */
  this.data_type = 2/*Z_UNKNOWN*/;
  /* adler32 value of the uncompressed data */
  this.adler = 0;
}

module.exports = ZStream;


/***/ }),

/***/ 10884:
/***/ (function(module) {

"use strict";


/** @type {import('.')} */
module.exports = [
	'Float32Array',
	'Float64Array',
	'Int8Array',
	'Int16Array',
	'Int32Array',
	'Uint8Array',
	'Uint8ClampedArray',
	'Uint16Array',
	'Uint32Array',
	'BigInt64Array',
	'BigUint64Array'
];


/***/ }),

/***/ 9964:
/***/ (function(module) {

// shim for using process in browser
var process = module.exports = {};

// cached from whatever global is present so that test runners that stub it
// don't break things.  But we need to wrap it in a try catch in case it is
// wrapped in strict mode code which doesn't define any globals.  It's inside a
// function because try/catches deoptimize in certain engines.

var cachedSetTimeout;
var cachedClearTimeout;

function defaultSetTimout() {
    throw new Error('setTimeout has not been defined');
}
function defaultClearTimeout () {
    throw new Error('clearTimeout has not been defined');
}
(function () {
    try {
        if (typeof setTimeout === 'function') {
            cachedSetTimeout = setTimeout;
        } else {
            cachedSetTimeout = defaultSetTimout;
        }
    } catch (e) {
        cachedSetTimeout = defaultSetTimout;
    }
    try {
        if (typeof clearTimeout === 'function') {
            cachedClearTimeout = clearTimeout;
        } else {
            cachedClearTimeout = defaultClearTimeout;
        }
    } catch (e) {
        cachedClearTimeout = defaultClearTimeout;
    }
} ())
function runTimeout(fun) {
    if (cachedSetTimeout === setTimeout) {
        //normal enviroments in sane situations
        return setTimeout(fun, 0);
    }
    // if setTimeout wasn't available but was latter defined
    if ((cachedSetTimeout === defaultSetTimout || !cachedSetTimeout) && setTimeout) {
        cachedSetTimeout = setTimeout;
        return setTimeout(fun, 0);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedSetTimeout(fun, 0);
    } catch(e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't trust the global object when called normally
            return cachedSetTimeout.call(null, fun, 0);
        } catch(e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error
            return cachedSetTimeout.call(this, fun, 0);
        }
    }


}
function runClearTimeout(marker) {
    if (cachedClearTimeout === clearTimeout) {
        //normal enviroments in sane situations
        return clearTimeout(marker);
    }
    // if clearTimeout wasn't available but was latter defined
    if ((cachedClearTimeout === defaultClearTimeout || !cachedClearTimeout) && clearTimeout) {
        cachedClearTimeout = clearTimeout;
        return clearTimeout(marker);
    }
    try {
        // when when somebody has screwed with setTimeout but no I.E. maddness
        return cachedClearTimeout(marker);
    } catch (e){
        try {
            // When we are in I.E. but the script has been evaled so I.E. doesn't  trust the global object when called normally
            return cachedClearTimeout.call(null, marker);
        } catch (e){
            // same as above but when it's a version of I.E. that must have the global object for 'this', hopfully our context correct otherwise it will throw a global error.
            // Some versions of I.E. have different rules for clearTimeout vs setTimeout
            return cachedClearTimeout.call(this, marker);
        }
    }



}
var queue = [];
var draining = false;
var currentQueue;
var queueIndex = -1;

function cleanUpNextTick() {
    if (!draining || !currentQueue) {
        return;
    }
    draining = false;
    if (currentQueue.length) {
        queue = currentQueue.concat(queue);
    } else {
        queueIndex = -1;
    }
    if (queue.length) {
        drainQueue();
    }
}

function drainQueue() {
    if (draining) {
        return;
    }
    var timeout = runTimeout(cleanUpNextTick);
    draining = true;

    var len = queue.length;
    while(len) {
        currentQueue = queue;
        queue = [];
        while (++queueIndex < len) {
            if (currentQueue) {
                currentQueue[queueIndex].run();
            }
        }
        queueIndex = -1;
        len = queue.length;
    }
    currentQueue = null;
    draining = false;
    runClearTimeout(timeout);
}

process.nextTick = function (fun) {
    var args = new Array(arguments.length - 1);
    if (arguments.length > 1) {
        for (var i = 1; i < arguments.length; i++) {
            args[i - 1] = arguments[i];
        }
    }
    queue.push(new Item(fun, args));
    if (queue.length === 1 && !draining) {
        runTimeout(drainQueue);
    }
};

// v8 likes predictible objects
function Item(fun, array) {
    this.fun = fun;
    this.array = array;
}
Item.prototype.run = function () {
    this.fun.apply(null, this.array);
};
process.title = 'browser';
process.browser = true;
process.env = {};
process.argv = [];
process.version = ''; // empty string to avoid regexp issues
process.versions = {};

function noop() {}

process.on = noop;
process.addListener = noop;
process.once = noop;
process.off = noop;
process.removeListener = noop;
process.removeAllListeners = noop;
process.emit = noop;
process.prependListener = noop;
process.prependOnceListener = noop;

process.listeners = function (name) { return [] }

process.binding = function (name) {
    throw new Error('process.binding is not supported');
};

process.cwd = function () { return '/' };
process.chdir = function (dir) {
    throw new Error('process.chdir is not supported');
};
process.umask = function() { return 0; };


/***/ }),

/***/ 83089:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var setFunctionName = __webpack_require__(95304);
var $TypeError = __webpack_require__(96785);

var $Object = Object;

module.exports = setFunctionName(function flags() {
	if (this == null || this !== $Object(this)) {
		throw new $TypeError('RegExp.prototype.flags getter called on non-object');
	}
	var result = '';
	if (this.hasIndices) {
		result += 'd';
	}
	if (this.global) {
		result += 'g';
	}
	if (this.ignoreCase) {
		result += 'i';
	}
	if (this.multiline) {
		result += 'm';
	}
	if (this.dotAll) {
		result += 's';
	}
	if (this.unicode) {
		result += 'u';
	}
	if (this.unicodeSets) {
		result += 'v';
	}
	if (this.sticky) {
		result += 'y';
	}
	return result;
}, 'get flags', true);



/***/ }),

/***/ 64607:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(77802);
var callBind = __webpack_require__(26601);

var implementation = __webpack_require__(83089);
var getPolyfill = __webpack_require__(7844);
var shim = __webpack_require__(51374);

var flagsBound = callBind(getPolyfill());

define(flagsBound, {
	getPolyfill: getPolyfill,
	implementation: implementation,
	shim: shim
});

module.exports = flagsBound;


/***/ }),

/***/ 7844:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var implementation = __webpack_require__(83089);

var supportsDescriptors = (__webpack_require__(77802).supportsDescriptors);
var $gOPD = Object.getOwnPropertyDescriptor;

module.exports = function getPolyfill() {
	if (supportsDescriptors && (/a/mig).flags === 'gim') {
		var descriptor = $gOPD(RegExp.prototype, 'flags');
		if (
			descriptor
			&& typeof descriptor.get === 'function'
			&& 'dotAll' in RegExp.prototype
			&& 'hasIndices' in RegExp.prototype
		) {
			/* eslint getter-return: 0 */
			var calls = '';
			var o = {};
			Object.defineProperty(o, 'hasIndices', {
				get: function () {
					calls += 'd';
				}
			});
			Object.defineProperty(o, 'sticky', {
				get: function () {
					calls += 'y';
				}
			});

			descriptor.get.call(o);

			if (calls === 'dy') {
				return descriptor.get;
			}
		}
	}
	return implementation;
};


/***/ }),

/***/ 51374:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var supportsDescriptors = (__webpack_require__(77802).supportsDescriptors);
var getPolyfill = __webpack_require__(7844);
var gOPD = Object.getOwnPropertyDescriptor;
var defineProperty = Object.defineProperty;
var TypeErr = TypeError;
var getProto = Object.getPrototypeOf;
var regex = /a/;

module.exports = function shimFlags() {
	if (!supportsDescriptors || !getProto) {
		throw new TypeErr('RegExp.prototype.flags requires a true ES5 environment that supports property descriptors');
	}
	var polyfill = getPolyfill();
	var proto = getProto(regex);
	var descriptor = gOPD(proto, 'flags');
	if (!descriptor || descriptor.get !== polyfill) {
		defineProperty(proto, 'flags', {
			configurable: true,
			enumerable: false,
			get: polyfill
		});
	}
	return polyfill;
};


/***/ }),

/***/ 61733:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];
;(function (sax) { // wrapper for non-node envs
  sax.parser = function (strict, opt) { return new SAXParser(strict, opt) }
  sax.SAXParser = SAXParser
  sax.SAXStream = SAXStream
  sax.createStream = createStream

  // When we pass the MAX_BUFFER_LENGTH position, start checking for buffer overruns.
  // When we check, schedule the next check for MAX_BUFFER_LENGTH - (max(buffer lengths)),
  // since that's the earliest that a buffer overrun could occur.  This way, checks are
  // as rare as required, but as often as necessary to ensure never crossing this bound.
  // Furthermore, buffers are only tested at most once per write(), so passing a very
  // large string into write() might have undesirable effects, but this is manageable by
  // the caller, so it is assumed to be safe.  Thus, a call to write() may, in the extreme
  // edge case, result in creating at most one complete copy of the string passed in.
  // Set to Infinity to have unlimited buffers.
  sax.MAX_BUFFER_LENGTH = 64 * 1024

  var buffers = [
    'comment', 'sgmlDecl', 'textNode', 'tagName', 'doctype',
    'procInstName', 'procInstBody', 'entity', 'attribName',
    'attribValue', 'cdata', 'script'
  ]

  sax.EVENTS = [
    'text',
    'processinginstruction',
    'sgmldeclaration',
    'doctype',
    'comment',
    'opentagstart',
    'attribute',
    'opentag',
    'closetag',
    'opencdata',
    'cdata',
    'closecdata',
    'error',
    'end',
    'ready',
    'script',
    'opennamespace',
    'closenamespace'
  ]

  function SAXParser (strict, opt) {
    if (!(this instanceof SAXParser)) {
      return new SAXParser(strict, opt)
    }

    var parser = this
    clearBuffers(parser)
    parser.q = parser.c = ''
    parser.bufferCheckPosition = sax.MAX_BUFFER_LENGTH
    parser.opt = opt || {}
    parser.opt.lowercase = parser.opt.lowercase || parser.opt.lowercasetags
    parser.looseCase = parser.opt.lowercase ? 'toLowerCase' : 'toUpperCase'
    parser.tags = []
    parser.closed = parser.closedRoot = parser.sawRoot = false
    parser.tag = parser.error = null
    parser.strict = !!strict
    parser.noscript = !!(strict || parser.opt.noscript)
    parser.state = S.BEGIN
    parser.strictEntities = parser.opt.strictEntities
    parser.ENTITIES = parser.strictEntities ? Object.create(sax.XML_ENTITIES) : Object.create(sax.ENTITIES)
    parser.attribList = []

    // namespaces form a prototype chain.
    // it always points at the current tag,
    // which protos to its parent tag.
    if (parser.opt.xmlns) {
      parser.ns = Object.create(rootNS)
    }

    // disallow unquoted attribute values if not otherwise configured
    // and strict mode is true
    if (parser.opt.unquotedAttributeValues === undefined) {
      parser.opt.unquotedAttributeValues = !strict;
    }

    // mostly just for error reporting
    parser.trackPosition = parser.opt.position !== false
    if (parser.trackPosition) {
      parser.position = parser.line = parser.column = 0
    }
    emit(parser, 'onready')
  }

  if (!Object.create) {
    Object.create = function (o) {
      function F () {}
      F.prototype = o
      var newf = new F()
      return newf
    }
  }

  if (!Object.keys) {
    Object.keys = function (o) {
      var a = []
      for (var i in o) if (o.hasOwnProperty(i)) a.push(i)
      return a
    }
  }

  function checkBufferLength (parser) {
    var maxAllowed = Math.max(sax.MAX_BUFFER_LENGTH, 10)
    var maxActual = 0
    for (var i = 0, l = buffers.length; i < l; i++) {
      var len = parser[buffers[i]].length
      if (len > maxAllowed) {
        // Text/cdata nodes can get big, and since they're buffered,
        // we can get here under normal conditions.
        // Avoid issues by emitting the text node now,
        // so at least it won't get any bigger.
        switch (buffers[i]) {
          case 'textNode':
            closeText(parser)
            break

          case 'cdata':
            emitNode(parser, 'oncdata', parser.cdata)
            parser.cdata = ''
            break

          case 'script':
            emitNode(parser, 'onscript', parser.script)
            parser.script = ''
            break

          default:
            error(parser, 'Max buffer length exceeded: ' + buffers[i])
        }
      }
      maxActual = Math.max(maxActual, len)
    }
    // schedule the next check for the earliest possible buffer overrun.
    var m = sax.MAX_BUFFER_LENGTH - maxActual
    parser.bufferCheckPosition = m + parser.position
  }

  function clearBuffers (parser) {
    for (var i = 0, l = buffers.length; i < l; i++) {
      parser[buffers[i]] = ''
    }
  }

  function flushBuffers (parser) {
    closeText(parser)
    if (parser.cdata !== '') {
      emitNode(parser, 'oncdata', parser.cdata)
      parser.cdata = ''
    }
    if (parser.script !== '') {
      emitNode(parser, 'onscript', parser.script)
      parser.script = ''
    }
  }

  SAXParser.prototype = {
    end: function () { end(this) },
    write: write,
    resume: function () { this.error = null; return this },
    close: function () { return this.write(null) },
    flush: function () { flushBuffers(this) }
  }

  var Stream
  try {
    Stream = (__webpack_require__(9760).Stream)
  } catch (ex) {
    Stream = function () {}
  }
  if (!Stream) Stream = function () {}

  var streamWraps = sax.EVENTS.filter(function (ev) {
    return ev !== 'error' && ev !== 'end'
  })

  function createStream (strict, opt) {
    return new SAXStream(strict, opt)
  }

  function SAXStream (strict, opt) {
    if (!(this instanceof SAXStream)) {
      return new SAXStream(strict, opt)
    }

    Stream.apply(this)

    this._parser = new SAXParser(strict, opt)
    this.writable = true
    this.readable = true

    var me = this

    this._parser.onend = function () {
      me.emit('end')
    }

    this._parser.onerror = function (er) {
      me.emit('error', er)

      // if didn't throw, then means error was handled.
      // go ahead and clear error, so we can write again.
      me._parser.error = null
    }

    this._decoder = null

    streamWraps.forEach(function (ev) {
      Object.defineProperty(me, 'on' + ev, {
        get: function () {
          return me._parser['on' + ev]
        },
        set: function (h) {
          if (!h) {
            me.removeAllListeners(ev)
            me._parser['on' + ev] = h
            return h
          }
          me.on(ev, h)
        },
        enumerable: true,
        configurable: false
      })
    })
  }

  SAXStream.prototype = Object.create(Stream.prototype, {
    constructor: {
      value: SAXStream
    }
  })

  SAXStream.prototype.write = function (data) {
    if (typeof Buffer === 'function' &&
      typeof Buffer.isBuffer === 'function' &&
      Buffer.isBuffer(data)) {
      if (!this._decoder) {
        var SD = (__webpack_require__(43143)/* .StringDecoder */ .I)
        this._decoder = new SD('utf8')
      }
      data = this._decoder.write(data)
    }

    this._parser.write(data.toString())
    this.emit('data', data)
    return true
  }

  SAXStream.prototype.end = function (chunk) {
    if (chunk && chunk.length) {
      this.write(chunk)
    }
    this._parser.end()
    return true
  }

  SAXStream.prototype.on = function (ev, handler) {
    var me = this
    if (!me._parser['on' + ev] && streamWraps.indexOf(ev) !== -1) {
      me._parser['on' + ev] = function () {
        var args = arguments.length === 1 ? [arguments[0]] : Array.apply(null, arguments)
        args.splice(0, 0, ev)
        me.emit.apply(me, args)
      }
    }

    return Stream.prototype.on.call(me, ev, handler)
  }

  // this really needs to be replaced with character classes.
  // XML allows all manner of ridiculous numbers and digits.
  var CDATA = '[CDATA['
  var DOCTYPE = 'DOCTYPE'
  var XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace'
  var XMLNS_NAMESPACE = 'http://www.w3.org/2000/xmlns/'
  var rootNS = { xml: XML_NAMESPACE, xmlns: XMLNS_NAMESPACE }

  // http://www.w3.org/TR/REC-xml/#NT-NameStartChar
  // This implementation works on strings, a single character at a time
  // as such, it cannot ever support astral-plane characters (10000-EFFFF)
  // without a significant breaking change to either this  parser, or the
  // JavaScript language.  Implementation of an emoji-capable xml parser
  // is left as an exercise for the reader.
  var nameStart = /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/

  var nameBody = /[:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/

  var entityStart = /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/
  var entityBody = /[#:_A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\u00B7\u0300-\u036F\u203F-\u2040.\d-]/

  function isWhitespace (c) {
    return c === ' ' || c === '\n' || c === '\r' || c === '\t'
  }

  function isQuote (c) {
    return c === '"' || c === '\''
  }

  function isAttribEnd (c) {
    return c === '>' || isWhitespace(c)
  }

  function isMatch (regex, c) {
    return regex.test(c)
  }

  function notMatch (regex, c) {
    return !isMatch(regex, c)
  }

  var S = 0
  sax.STATE = {
    BEGIN: S++, // leading byte order mark or whitespace
    BEGIN_WHITESPACE: S++, // leading whitespace
    TEXT: S++, // general stuff
    TEXT_ENTITY: S++, // &amp and such.
    OPEN_WAKA: S++, // <
    SGML_DECL: S++, // <!BLARG
    SGML_DECL_QUOTED: S++, // <!BLARG foo "bar
    DOCTYPE: S++, // <!DOCTYPE
    DOCTYPE_QUOTED: S++, // <!DOCTYPE "//blah
    DOCTYPE_DTD: S++, // <!DOCTYPE "//blah" [ ...
    DOCTYPE_DTD_QUOTED: S++, // <!DOCTYPE "//blah" [ "foo
    COMMENT_STARTING: S++, // <!-
    COMMENT: S++, // <!--
    COMMENT_ENDING: S++, // <!-- blah -
    COMMENT_ENDED: S++, // <!-- blah --
    CDATA: S++, // <![CDATA[ something
    CDATA_ENDING: S++, // ]
    CDATA_ENDING_2: S++, // ]]
    PROC_INST: S++, // <?hi
    PROC_INST_BODY: S++, // <?hi there
    PROC_INST_ENDING: S++, // <?hi "there" ?
    OPEN_TAG: S++, // <strong
    OPEN_TAG_SLASH: S++, // <strong /
    ATTRIB: S++, // <a
    ATTRIB_NAME: S++, // <a foo
    ATTRIB_NAME_SAW_WHITE: S++, // <a foo _
    ATTRIB_VALUE: S++, // <a foo=
    ATTRIB_VALUE_QUOTED: S++, // <a foo="bar
    ATTRIB_VALUE_CLOSED: S++, // <a foo="bar"
    ATTRIB_VALUE_UNQUOTED: S++, // <a foo=bar
    ATTRIB_VALUE_ENTITY_Q: S++, // <foo bar="&quot;"
    ATTRIB_VALUE_ENTITY_U: S++, // <foo bar=&quot
    CLOSE_TAG: S++, // </a
    CLOSE_TAG_SAW_WHITE: S++, // </a   >
    SCRIPT: S++, // <script> ...
    SCRIPT_ENDING: S++ // <script> ... <
  }

  sax.XML_ENTITIES = {
    'amp': '&',
    'gt': '>',
    'lt': '<',
    'quot': '"',
    'apos': "'"
  }

  sax.ENTITIES = {
    'amp': '&',
    'gt': '>',
    'lt': '<',
    'quot': '"',
    'apos': "'",
    'AElig': 198,
    'Aacute': 193,
    'Acirc': 194,
    'Agrave': 192,
    'Aring': 197,
    'Atilde': 195,
    'Auml': 196,
    'Ccedil': 199,
    'ETH': 208,
    'Eacute': 201,
    'Ecirc': 202,
    'Egrave': 200,
    'Euml': 203,
    'Iacute': 205,
    'Icirc': 206,
    'Igrave': 204,
    'Iuml': 207,
    'Ntilde': 209,
    'Oacute': 211,
    'Ocirc': 212,
    'Ograve': 210,
    'Oslash': 216,
    'Otilde': 213,
    'Ouml': 214,
    'THORN': 222,
    'Uacute': 218,
    'Ucirc': 219,
    'Ugrave': 217,
    'Uuml': 220,
    'Yacute': 221,
    'aacute': 225,
    'acirc': 226,
    'aelig': 230,
    'agrave': 224,
    'aring': 229,
    'atilde': 227,
    'auml': 228,
    'ccedil': 231,
    'eacute': 233,
    'ecirc': 234,
    'egrave': 232,
    'eth': 240,
    'euml': 235,
    'iacute': 237,
    'icirc': 238,
    'igrave': 236,
    'iuml': 239,
    'ntilde': 241,
    'oacute': 243,
    'ocirc': 244,
    'ograve': 242,
    'oslash': 248,
    'otilde': 245,
    'ouml': 246,
    'szlig': 223,
    'thorn': 254,
    'uacute': 250,
    'ucirc': 251,
    'ugrave': 249,
    'uuml': 252,
    'yacute': 253,
    'yuml': 255,
    'copy': 169,
    'reg': 174,
    'nbsp': 160,
    'iexcl': 161,
    'cent': 162,
    'pound': 163,
    'curren': 164,
    'yen': 165,
    'brvbar': 166,
    'sect': 167,
    'uml': 168,
    'ordf': 170,
    'laquo': 171,
    'not': 172,
    'shy': 173,
    'macr': 175,
    'deg': 176,
    'plusmn': 177,
    'sup1': 185,
    'sup2': 178,
    'sup3': 179,
    'acute': 180,
    'micro': 181,
    'para': 182,
    'middot': 183,
    'cedil': 184,
    'ordm': 186,
    'raquo': 187,
    'frac14': 188,
    'frac12': 189,
    'frac34': 190,
    'iquest': 191,
    'times': 215,
    'divide': 247,
    'OElig': 338,
    'oelig': 339,
    'Scaron': 352,
    'scaron': 353,
    'Yuml': 376,
    'fnof': 402,
    'circ': 710,
    'tilde': 732,
    'Alpha': 913,
    'Beta': 914,
    'Gamma': 915,
    'Delta': 916,
    'Epsilon': 917,
    'Zeta': 918,
    'Eta': 919,
    'Theta': 920,
    'Iota': 921,
    'Kappa': 922,
    'Lambda': 923,
    'Mu': 924,
    'Nu': 925,
    'Xi': 926,
    'Omicron': 927,
    'Pi': 928,
    'Rho': 929,
    'Sigma': 931,
    'Tau': 932,
    'Upsilon': 933,
    'Phi': 934,
    'Chi': 935,
    'Psi': 936,
    'Omega': 937,
    'alpha': 945,
    'beta': 946,
    'gamma': 947,
    'delta': 948,
    'epsilon': 949,
    'zeta': 950,
    'eta': 951,
    'theta': 952,
    'iota': 953,
    'kappa': 954,
    'lambda': 955,
    'mu': 956,
    'nu': 957,
    'xi': 958,
    'omicron': 959,
    'pi': 960,
    'rho': 961,
    'sigmaf': 962,
    'sigma': 963,
    'tau': 964,
    'upsilon': 965,
    'phi': 966,
    'chi': 967,
    'psi': 968,
    'omega': 969,
    'thetasym': 977,
    'upsih': 978,
    'piv': 982,
    'ensp': 8194,
    'emsp': 8195,
    'thinsp': 8201,
    'zwnj': 8204,
    'zwj': 8205,
    'lrm': 8206,
    'rlm': 8207,
    'ndash': 8211,
    'mdash': 8212,
    'lsquo': 8216,
    'rsquo': 8217,
    'sbquo': 8218,
    'ldquo': 8220,
    'rdquo': 8221,
    'bdquo': 8222,
    'dagger': 8224,
    'Dagger': 8225,
    'bull': 8226,
    'hellip': 8230,
    'permil': 8240,
    'prime': 8242,
    'Prime': 8243,
    'lsaquo': 8249,
    'rsaquo': 8250,
    'oline': 8254,
    'frasl': 8260,
    'euro': 8364,
    'image': 8465,
    'weierp': 8472,
    'real': 8476,
    'trade': 8482,
    'alefsym': 8501,
    'larr': 8592,
    'uarr': 8593,
    'rarr': 8594,
    'darr': 8595,
    'harr': 8596,
    'crarr': 8629,
    'lArr': 8656,
    'uArr': 8657,
    'rArr': 8658,
    'dArr': 8659,
    'hArr': 8660,
    'forall': 8704,
    'part': 8706,
    'exist': 8707,
    'empty': 8709,
    'nabla': 8711,
    'isin': 8712,
    'notin': 8713,
    'ni': 8715,
    'prod': 8719,
    'sum': 8721,
    'minus': 8722,
    'lowast': 8727,
    'radic': 8730,
    'prop': 8733,
    'infin': 8734,
    'ang': 8736,
    'and': 8743,
    'or': 8744,
    'cap': 8745,
    'cup': 8746,
    'int': 8747,
    'there4': 8756,
    'sim': 8764,
    'cong': 8773,
    'asymp': 8776,
    'ne': 8800,
    'equiv': 8801,
    'le': 8804,
    'ge': 8805,
    'sub': 8834,
    'sup': 8835,
    'nsub': 8836,
    'sube': 8838,
    'supe': 8839,
    'oplus': 8853,
    'otimes': 8855,
    'perp': 8869,
    'sdot': 8901,
    'lceil': 8968,
    'rceil': 8969,
    'lfloor': 8970,
    'rfloor': 8971,
    'lang': 9001,
    'rang': 9002,
    'loz': 9674,
    'spades': 9824,
    'clubs': 9827,
    'hearts': 9829,
    'diams': 9830
  }

  Object.keys(sax.ENTITIES).forEach(function (key) {
    var e = sax.ENTITIES[key]
    var s = typeof e === 'number' ? String.fromCharCode(e) : e
    sax.ENTITIES[key] = s
  })

  for (var s in sax.STATE) {
    sax.STATE[sax.STATE[s]] = s
  }

  // shorthand
  S = sax.STATE

  function emit (parser, event, data) {
    parser[event] && parser[event](data)
  }

  function emitNode (parser, nodeType, data) {
    if (parser.textNode) closeText(parser)
    emit(parser, nodeType, data)
  }

  function closeText (parser) {
    parser.textNode = textopts(parser.opt, parser.textNode)
    if (parser.textNode) emit(parser, 'ontext', parser.textNode)
    parser.textNode = ''
  }

  function textopts (opt, text) {
    if (opt.trim) text = text.trim()
    if (opt.normalize) text = text.replace(/\s+/g, ' ')
    return text
  }

  function error (parser, er) {
    closeText(parser)
    if (parser.trackPosition) {
      er += '\nLine: ' + parser.line +
        '\nColumn: ' + parser.column +
        '\nChar: ' + parser.c
    }
    er = new Error(er)
    parser.error = er
    emit(parser, 'onerror', er)
    return parser
  }

  function end (parser) {
    if (parser.sawRoot && !parser.closedRoot) strictFail(parser, 'Unclosed root tag')
    if ((parser.state !== S.BEGIN) &&
      (parser.state !== S.BEGIN_WHITESPACE) &&
      (parser.state !== S.TEXT)) {
      error(parser, 'Unexpected end')
    }
    closeText(parser)
    parser.c = ''
    parser.closed = true
    emit(parser, 'onend')
    SAXParser.call(parser, parser.strict, parser.opt)
    return parser
  }

  function strictFail (parser, message) {
    if (typeof parser !== 'object' || !(parser instanceof SAXParser)) {
      throw new Error('bad call to strictFail')
    }
    if (parser.strict) {
      error(parser, message)
    }
  }

  function newTag (parser) {
    if (!parser.strict) parser.tagName = parser.tagName[parser.looseCase]()
    var parent = parser.tags[parser.tags.length - 1] || parser
    var tag = parser.tag = { name: parser.tagName, attributes: {} }

    // will be overridden if tag contails an xmlns="foo" or xmlns:foo="bar"
    if (parser.opt.xmlns) {
      tag.ns = parent.ns
    }
    parser.attribList.length = 0
    emitNode(parser, 'onopentagstart', tag)
  }

  function qname (name, attribute) {
    var i = name.indexOf(':')
    var qualName = i < 0 ? [ '', name ] : name.split(':')
    var prefix = qualName[0]
    var local = qualName[1]

    // <x "xmlns"="http://foo">
    if (attribute && name === 'xmlns') {
      prefix = 'xmlns'
      local = ''
    }

    return { prefix: prefix, local: local }
  }

  function attrib (parser) {
    if (!parser.strict) {
      parser.attribName = parser.attribName[parser.looseCase]()
    }

    if (parser.attribList.indexOf(parser.attribName) !== -1 ||
      parser.tag.attributes.hasOwnProperty(parser.attribName)) {
      parser.attribName = parser.attribValue = ''
      return
    }

    if (parser.opt.xmlns) {
      var qn = qname(parser.attribName, true)
      var prefix = qn.prefix
      var local = qn.local

      if (prefix === 'xmlns') {
        // namespace binding attribute. push the binding into scope
        if (local === 'xml' && parser.attribValue !== XML_NAMESPACE) {
          strictFail(parser,
            'xml: prefix must be bound to ' + XML_NAMESPACE + '\n' +
            'Actual: ' + parser.attribValue)
        } else if (local === 'xmlns' && parser.attribValue !== XMLNS_NAMESPACE) {
          strictFail(parser,
            'xmlns: prefix must be bound to ' + XMLNS_NAMESPACE + '\n' +
            'Actual: ' + parser.attribValue)
        } else {
          var tag = parser.tag
          var parent = parser.tags[parser.tags.length - 1] || parser
          if (tag.ns === parent.ns) {
            tag.ns = Object.create(parent.ns)
          }
          tag.ns[local] = parser.attribValue
        }
      }

      // defer onattribute events until all attributes have been seen
      // so any new bindings can take effect. preserve attribute order
      // so deferred events can be emitted in document order
      parser.attribList.push([parser.attribName, parser.attribValue])
    } else {
      // in non-xmlns mode, we can emit the event right away
      parser.tag.attributes[parser.attribName] = parser.attribValue
      emitNode(parser, 'onattribute', {
        name: parser.attribName,
        value: parser.attribValue
      })
    }

    parser.attribName = parser.attribValue = ''
  }

  function openTag (parser, selfClosing) {
    if (parser.opt.xmlns) {
      // emit namespace binding events
      var tag = parser.tag

      // add namespace info to tag
      var qn = qname(parser.tagName)
      tag.prefix = qn.prefix
      tag.local = qn.local
      tag.uri = tag.ns[qn.prefix] || ''

      if (tag.prefix && !tag.uri) {
        strictFail(parser, 'Unbound namespace prefix: ' +
          JSON.stringify(parser.tagName))
        tag.uri = qn.prefix
      }

      var parent = parser.tags[parser.tags.length - 1] || parser
      if (tag.ns && parent.ns !== tag.ns) {
        Object.keys(tag.ns).forEach(function (p) {
          emitNode(parser, 'onopennamespace', {
            prefix: p,
            uri: tag.ns[p]
          })
        })
      }

      // handle deferred onattribute events
      // Note: do not apply default ns to attributes:
      //   http://www.w3.org/TR/REC-xml-names/#defaulting
      for (var i = 0, l = parser.attribList.length; i < l; i++) {
        var nv = parser.attribList[i]
        var name = nv[0]
        var value = nv[1]
        var qualName = qname(name, true)
        var prefix = qualName.prefix
        var local = qualName.local
        var uri = prefix === '' ? '' : (tag.ns[prefix] || '')
        var a = {
          name: name,
          value: value,
          prefix: prefix,
          local: local,
          uri: uri
        }

        // if there's any attributes with an undefined namespace,
        // then fail on them now.
        if (prefix && prefix !== 'xmlns' && !uri) {
          strictFail(parser, 'Unbound namespace prefix: ' +
            JSON.stringify(prefix))
          a.uri = prefix
        }
        parser.tag.attributes[name] = a
        emitNode(parser, 'onattribute', a)
      }
      parser.attribList.length = 0
    }

    parser.tag.isSelfClosing = !!selfClosing

    // process the tag
    parser.sawRoot = true
    parser.tags.push(parser.tag)
    emitNode(parser, 'onopentag', parser.tag)
    if (!selfClosing) {
      // special case for <script> in non-strict mode.
      if (!parser.noscript && parser.tagName.toLowerCase() === 'script') {
        parser.state = S.SCRIPT
      } else {
        parser.state = S.TEXT
      }
      parser.tag = null
      parser.tagName = ''
    }
    parser.attribName = parser.attribValue = ''
    parser.attribList.length = 0
  }

  function closeTag (parser) {
    if (!parser.tagName) {
      strictFail(parser, 'Weird empty close tag.')
      parser.textNode += '</>'
      parser.state = S.TEXT
      return
    }

    if (parser.script) {
      if (parser.tagName !== 'script') {
        parser.script += '</' + parser.tagName + '>'
        parser.tagName = ''
        parser.state = S.SCRIPT
        return
      }
      emitNode(parser, 'onscript', parser.script)
      parser.script = ''
    }

    // first make sure that the closing tag actually exists.
    // <a><b></c></b></a> will close everything, otherwise.
    var t = parser.tags.length
    var tagName = parser.tagName
    if (!parser.strict) {
      tagName = tagName[parser.looseCase]()
    }
    var closeTo = tagName
    while (t--) {
      var close = parser.tags[t]
      if (close.name !== closeTo) {
        // fail the first time in strict mode
        strictFail(parser, 'Unexpected close tag')
      } else {
        break
      }
    }

    // didn't find it.  we already failed for strict, so just abort.
    if (t < 0) {
      strictFail(parser, 'Unmatched closing tag: ' + parser.tagName)
      parser.textNode += '</' + parser.tagName + '>'
      parser.state = S.TEXT
      return
    }
    parser.tagName = tagName
    var s = parser.tags.length
    while (s-- > t) {
      var tag = parser.tag = parser.tags.pop()
      parser.tagName = parser.tag.name
      emitNode(parser, 'onclosetag', parser.tagName)

      var x = {}
      for (var i in tag.ns) {
        x[i] = tag.ns[i]
      }

      var parent = parser.tags[parser.tags.length - 1] || parser
      if (parser.opt.xmlns && tag.ns !== parent.ns) {
        // remove namespace bindings introduced by tag
        Object.keys(tag.ns).forEach(function (p) {
          var n = tag.ns[p]
          emitNode(parser, 'onclosenamespace', { prefix: p, uri: n })
        })
      }
    }
    if (t === 0) parser.closedRoot = true
    parser.tagName = parser.attribValue = parser.attribName = ''
    parser.attribList.length = 0
    parser.state = S.TEXT
  }

  function parseEntity (parser) {
    var entity = parser.entity
    var entityLC = entity.toLowerCase()
    var num
    var numStr = ''

    if (parser.ENTITIES[entity]) {
      return parser.ENTITIES[entity]
    }
    if (parser.ENTITIES[entityLC]) {
      return parser.ENTITIES[entityLC]
    }
    entity = entityLC
    if (entity.charAt(0) === '#') {
      if (entity.charAt(1) === 'x') {
        entity = entity.slice(2)
        num = parseInt(entity, 16)
        numStr = num.toString(16)
      } else {
        entity = entity.slice(1)
        num = parseInt(entity, 10)
        numStr = num.toString(10)
      }
    }
    entity = entity.replace(/^0+/, '')
    if (isNaN(num) || numStr.toLowerCase() !== entity) {
      strictFail(parser, 'Invalid character entity')
      return '&' + parser.entity + ';'
    }

    return String.fromCodePoint(num)
  }

  function beginWhiteSpace (parser, c) {
    if (c === '<') {
      parser.state = S.OPEN_WAKA
      parser.startTagPosition = parser.position
    } else if (!isWhitespace(c)) {
      // have to process this as a text node.
      // weird, but happens.
      strictFail(parser, 'Non-whitespace before first tag.')
      parser.textNode = c
      parser.state = S.TEXT
    }
  }

  function charAt (chunk, i) {
    var result = ''
    if (i < chunk.length) {
      result = chunk.charAt(i)
    }
    return result
  }

  function write (chunk) {
    var parser = this
    if (this.error) {
      throw this.error
    }
    if (parser.closed) {
      return error(parser,
        'Cannot write after close. Assign an onready handler.')
    }
    if (chunk === null) {
      return end(parser)
    }
    if (typeof chunk === 'object') {
      chunk = chunk.toString()
    }
    var i = 0
    var c = ''
    while (true) {
      c = charAt(chunk, i++)
      parser.c = c

      if (!c) {
        break
      }

      if (parser.trackPosition) {
        parser.position++
        if (c === '\n') {
          parser.line++
          parser.column = 0
        } else {
          parser.column++
        }
      }

      switch (parser.state) {
        case S.BEGIN:
          parser.state = S.BEGIN_WHITESPACE
          if (c === '\uFEFF') {
            continue
          }
          beginWhiteSpace(parser, c)
          continue

        case S.BEGIN_WHITESPACE:
          beginWhiteSpace(parser, c)
          continue

        case S.TEXT:
          if (parser.sawRoot && !parser.closedRoot) {
            var starti = i - 1
            while (c && c !== '<' && c !== '&') {
              c = charAt(chunk, i++)
              if (c && parser.trackPosition) {
                parser.position++
                if (c === '\n') {
                  parser.line++
                  parser.column = 0
                } else {
                  parser.column++
                }
              }
            }
            parser.textNode += chunk.substring(starti, i - 1)
          }
          if (c === '<' && !(parser.sawRoot && parser.closedRoot && !parser.strict)) {
            parser.state = S.OPEN_WAKA
            parser.startTagPosition = parser.position
          } else {
            if (!isWhitespace(c) && (!parser.sawRoot || parser.closedRoot)) {
              strictFail(parser, 'Text data outside of root node.')
            }
            if (c === '&') {
              parser.state = S.TEXT_ENTITY
            } else {
              parser.textNode += c
            }
          }
          continue

        case S.SCRIPT:
          // only non-strict
          if (c === '<') {
            parser.state = S.SCRIPT_ENDING
          } else {
            parser.script += c
          }
          continue

        case S.SCRIPT_ENDING:
          if (c === '/') {
            parser.state = S.CLOSE_TAG
          } else {
            parser.script += '<' + c
            parser.state = S.SCRIPT
          }
          continue

        case S.OPEN_WAKA:
          // either a /, ?, !, or text is coming next.
          if (c === '!') {
            parser.state = S.SGML_DECL
            parser.sgmlDecl = ''
          } else if (isWhitespace(c)) {
            // wait for it...
          } else if (isMatch(nameStart, c)) {
            parser.state = S.OPEN_TAG
            parser.tagName = c
          } else if (c === '/') {
            parser.state = S.CLOSE_TAG
            parser.tagName = ''
          } else if (c === '?') {
            parser.state = S.PROC_INST
            parser.procInstName = parser.procInstBody = ''
          } else {
            strictFail(parser, 'Unencoded <')
            // if there was some whitespace, then add that in.
            if (parser.startTagPosition + 1 < parser.position) {
              var pad = parser.position - parser.startTagPosition
              c = new Array(pad).join(' ') + c
            }
            parser.textNode += '<' + c
            parser.state = S.TEXT
          }
          continue

        case S.SGML_DECL:
          if (parser.sgmlDecl + c === '--') {
            parser.state = S.COMMENT
            parser.comment = ''
            parser.sgmlDecl = ''
            continue;
          }

          if (parser.doctype && parser.doctype !== true && parser.sgmlDecl) {
            parser.state = S.DOCTYPE_DTD
            parser.doctype += '<!' + parser.sgmlDecl + c
            parser.sgmlDecl = ''
          } else if ((parser.sgmlDecl + c).toUpperCase() === CDATA) {
            emitNode(parser, 'onopencdata')
            parser.state = S.CDATA
            parser.sgmlDecl = ''
            parser.cdata = ''
          } else if ((parser.sgmlDecl + c).toUpperCase() === DOCTYPE) {
            parser.state = S.DOCTYPE
            if (parser.doctype || parser.sawRoot) {
              strictFail(parser,
                'Inappropriately located doctype declaration')
            }
            parser.doctype = ''
            parser.sgmlDecl = ''
          } else if (c === '>') {
            emitNode(parser, 'onsgmldeclaration', parser.sgmlDecl)
            parser.sgmlDecl = ''
            parser.state = S.TEXT
          } else if (isQuote(c)) {
            parser.state = S.SGML_DECL_QUOTED
            parser.sgmlDecl += c
          } else {
            parser.sgmlDecl += c
          }
          continue

        case S.SGML_DECL_QUOTED:
          if (c === parser.q) {
            parser.state = S.SGML_DECL
            parser.q = ''
          }
          parser.sgmlDecl += c
          continue

        case S.DOCTYPE:
          if (c === '>') {
            parser.state = S.TEXT
            emitNode(parser, 'ondoctype', parser.doctype)
            parser.doctype = true // just remember that we saw it.
          } else {
            parser.doctype += c
            if (c === '[') {
              parser.state = S.DOCTYPE_DTD
            } else if (isQuote(c)) {
              parser.state = S.DOCTYPE_QUOTED
              parser.q = c
            }
          }
          continue

        case S.DOCTYPE_QUOTED:
          parser.doctype += c
          if (c === parser.q) {
            parser.q = ''
            parser.state = S.DOCTYPE
          }
          continue

        case S.DOCTYPE_DTD:
          if (c === ']') {
            parser.doctype += c
            parser.state = S.DOCTYPE
          } else if (c === '<') {
            parser.state = S.OPEN_WAKA
            parser.startTagPosition = parser.position
          } else if (isQuote(c)) {
            parser.doctype += c
            parser.state = S.DOCTYPE_DTD_QUOTED
            parser.q = c
          } else {
            parser.doctype += c
          }
          continue

        case S.DOCTYPE_DTD_QUOTED:
          parser.doctype += c
          if (c === parser.q) {
            parser.state = S.DOCTYPE_DTD
            parser.q = ''
          }
          continue

        case S.COMMENT:
          if (c === '-') {
            parser.state = S.COMMENT_ENDING
          } else {
            parser.comment += c
          }
          continue

        case S.COMMENT_ENDING:
          if (c === '-') {
            parser.state = S.COMMENT_ENDED
            parser.comment = textopts(parser.opt, parser.comment)
            if (parser.comment) {
              emitNode(parser, 'oncomment', parser.comment)
            }
            parser.comment = ''
          } else {
            parser.comment += '-' + c
            parser.state = S.COMMENT
          }
          continue

        case S.COMMENT_ENDED:
          if (c !== '>') {
            strictFail(parser, 'Malformed comment')
            // allow <!-- blah -- bloo --> in non-strict mode,
            // which is a comment of " blah -- bloo "
            parser.comment += '--' + c
            parser.state = S.COMMENT
          } else if (parser.doctype && parser.doctype !== true) {
            parser.state = S.DOCTYPE_DTD
          } else {
            parser.state = S.TEXT
          }
          continue

        case S.CDATA:
          if (c === ']') {
            parser.state = S.CDATA_ENDING
          } else {
            parser.cdata += c
          }
          continue

        case S.CDATA_ENDING:
          if (c === ']') {
            parser.state = S.CDATA_ENDING_2
          } else {
            parser.cdata += ']' + c
            parser.state = S.CDATA
          }
          continue

        case S.CDATA_ENDING_2:
          if (c === '>') {
            if (parser.cdata) {
              emitNode(parser, 'oncdata', parser.cdata)
            }
            emitNode(parser, 'onclosecdata')
            parser.cdata = ''
            parser.state = S.TEXT
          } else if (c === ']') {
            parser.cdata += ']'
          } else {
            parser.cdata += ']]' + c
            parser.state = S.CDATA
          }
          continue

        case S.PROC_INST:
          if (c === '?') {
            parser.state = S.PROC_INST_ENDING
          } else if (isWhitespace(c)) {
            parser.state = S.PROC_INST_BODY
          } else {
            parser.procInstName += c
          }
          continue

        case S.PROC_INST_BODY:
          if (!parser.procInstBody && isWhitespace(c)) {
            continue
          } else if (c === '?') {
            parser.state = S.PROC_INST_ENDING
          } else {
            parser.procInstBody += c
          }
          continue

        case S.PROC_INST_ENDING:
          if (c === '>') {
            emitNode(parser, 'onprocessinginstruction', {
              name: parser.procInstName,
              body: parser.procInstBody
            })
            parser.procInstName = parser.procInstBody = ''
            parser.state = S.TEXT
          } else {
            parser.procInstBody += '?' + c
            parser.state = S.PROC_INST_BODY
          }
          continue

        case S.OPEN_TAG:
          if (isMatch(nameBody, c)) {
            parser.tagName += c
          } else {
            newTag(parser)
            if (c === '>') {
              openTag(parser)
            } else if (c === '/') {
              parser.state = S.OPEN_TAG_SLASH
            } else {
              if (!isWhitespace(c)) {
                strictFail(parser, 'Invalid character in tag name')
              }
              parser.state = S.ATTRIB
            }
          }
          continue

        case S.OPEN_TAG_SLASH:
          if (c === '>') {
            openTag(parser, true)
            closeTag(parser)
          } else {
            strictFail(parser, 'Forward-slash in opening tag not followed by >')
            parser.state = S.ATTRIB
          }
          continue

        case S.ATTRIB:
          // haven't read the attribute name yet.
          if (isWhitespace(c)) {
            continue
          } else if (c === '>') {
            openTag(parser)
          } else if (c === '/') {
            parser.state = S.OPEN_TAG_SLASH
          } else if (isMatch(nameStart, c)) {
            parser.attribName = c
            parser.attribValue = ''
            parser.state = S.ATTRIB_NAME
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_NAME:
          if (c === '=') {
            parser.state = S.ATTRIB_VALUE
          } else if (c === '>') {
            strictFail(parser, 'Attribute without value')
            parser.attribValue = parser.attribName
            attrib(parser)
            openTag(parser)
          } else if (isWhitespace(c)) {
            parser.state = S.ATTRIB_NAME_SAW_WHITE
          } else if (isMatch(nameBody, c)) {
            parser.attribName += c
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_NAME_SAW_WHITE:
          if (c === '=') {
            parser.state = S.ATTRIB_VALUE
          } else if (isWhitespace(c)) {
            continue
          } else {
            strictFail(parser, 'Attribute without value')
            parser.tag.attributes[parser.attribName] = ''
            parser.attribValue = ''
            emitNode(parser, 'onattribute', {
              name: parser.attribName,
              value: ''
            })
            parser.attribName = ''
            if (c === '>') {
              openTag(parser)
            } else if (isMatch(nameStart, c)) {
              parser.attribName = c
              parser.state = S.ATTRIB_NAME
            } else {
              strictFail(parser, 'Invalid attribute name')
              parser.state = S.ATTRIB
            }
          }
          continue

        case S.ATTRIB_VALUE:
          if (isWhitespace(c)) {
            continue
          } else if (isQuote(c)) {
            parser.q = c
            parser.state = S.ATTRIB_VALUE_QUOTED
          } else {
            if (!parser.opt.unquotedAttributeValues) {
              error(parser, 'Unquoted attribute value')
            }
            parser.state = S.ATTRIB_VALUE_UNQUOTED
            parser.attribValue = c
          }
          continue

        case S.ATTRIB_VALUE_QUOTED:
          if (c !== parser.q) {
            if (c === '&') {
              parser.state = S.ATTRIB_VALUE_ENTITY_Q
            } else {
              parser.attribValue += c
            }
            continue
          }
          attrib(parser)
          parser.q = ''
          parser.state = S.ATTRIB_VALUE_CLOSED
          continue

        case S.ATTRIB_VALUE_CLOSED:
          if (isWhitespace(c)) {
            parser.state = S.ATTRIB
          } else if (c === '>') {
            openTag(parser)
          } else if (c === '/') {
            parser.state = S.OPEN_TAG_SLASH
          } else if (isMatch(nameStart, c)) {
            strictFail(parser, 'No whitespace between attributes')
            parser.attribName = c
            parser.attribValue = ''
            parser.state = S.ATTRIB_NAME
          } else {
            strictFail(parser, 'Invalid attribute name')
          }
          continue

        case S.ATTRIB_VALUE_UNQUOTED:
          if (!isAttribEnd(c)) {
            if (c === '&') {
              parser.state = S.ATTRIB_VALUE_ENTITY_U
            } else {
              parser.attribValue += c
            }
            continue
          }
          attrib(parser)
          if (c === '>') {
            openTag(parser)
          } else {
            parser.state = S.ATTRIB
          }
          continue

        case S.CLOSE_TAG:
          if (!parser.tagName) {
            if (isWhitespace(c)) {
              continue
            } else if (notMatch(nameStart, c)) {
              if (parser.script) {
                parser.script += '</' + c
                parser.state = S.SCRIPT
              } else {
                strictFail(parser, 'Invalid tagname in closing tag.')
              }
            } else {
              parser.tagName = c
            }
          } else if (c === '>') {
            closeTag(parser)
          } else if (isMatch(nameBody, c)) {
            parser.tagName += c
          } else if (parser.script) {
            parser.script += '</' + parser.tagName
            parser.tagName = ''
            parser.state = S.SCRIPT
          } else {
            if (!isWhitespace(c)) {
              strictFail(parser, 'Invalid tagname in closing tag')
            }
            parser.state = S.CLOSE_TAG_SAW_WHITE
          }
          continue

        case S.CLOSE_TAG_SAW_WHITE:
          if (isWhitespace(c)) {
            continue
          }
          if (c === '>') {
            closeTag(parser)
          } else {
            strictFail(parser, 'Invalid characters in closing tag')
          }
          continue

        case S.TEXT_ENTITY:
        case S.ATTRIB_VALUE_ENTITY_Q:
        case S.ATTRIB_VALUE_ENTITY_U:
          var returnState
          var buffer
          switch (parser.state) {
            case S.TEXT_ENTITY:
              returnState = S.TEXT
              buffer = 'textNode'
              break

            case S.ATTRIB_VALUE_ENTITY_Q:
              returnState = S.ATTRIB_VALUE_QUOTED
              buffer = 'attribValue'
              break

            case S.ATTRIB_VALUE_ENTITY_U:
              returnState = S.ATTRIB_VALUE_UNQUOTED
              buffer = 'attribValue'
              break
          }

          if (c === ';') {
            var parsedEntity = parseEntity(parser)
            if (parser.opt.unparsedEntities && !Object.values(sax.XML_ENTITIES).includes(parsedEntity)) {
              parser.entity = ''
              parser.state = returnState
              parser.write(parsedEntity)
            } else {
              parser[buffer] += parsedEntity
              parser.entity = ''
              parser.state = returnState
            }
          } else if (isMatch(parser.entity.length ? entityBody : entityStart, c)) {
            parser.entity += c
          } else {
            strictFail(parser, 'Invalid character in entity name')
            parser[buffer] += '&' + parser.entity + c
            parser.entity = ''
            parser.state = returnState
          }

          continue

        default: /* istanbul ignore next */ {
          throw new Error(parser, 'Unknown state: ' + parser.state)
        }
      }
    } // while

    if (parser.position >= parser.bufferCheckPosition) {
      checkBufferLength(parser)
    }
    return parser
  }

  /*! http://mths.be/fromcodepoint v0.1.0 by @mathias */
  /* istanbul ignore next */
  if (!String.fromCodePoint) {
    (function () {
      var stringFromCharCode = String.fromCharCode
      var floor = Math.floor
      var fromCodePoint = function () {
        var MAX_SIZE = 0x4000
        var codeUnits = []
        var highSurrogate
        var lowSurrogate
        var index = -1
        var length = arguments.length
        if (!length) {
          return ''
        }
        var result = ''
        while (++index < length) {
          var codePoint = Number(arguments[index])
          if (
            !isFinite(codePoint) || // `NaN`, `+Infinity`, or `-Infinity`
            codePoint < 0 || // not a valid Unicode code point
            codePoint > 0x10FFFF || // not a valid Unicode code point
            floor(codePoint) !== codePoint // not an integer
          ) {
            throw RangeError('Invalid code point: ' + codePoint)
          }
          if (codePoint <= 0xFFFF) { // BMP code point
            codeUnits.push(codePoint)
          } else { // Astral code point; split in surrogate halves
            // http://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
            codePoint -= 0x10000
            highSurrogate = (codePoint >> 10) + 0xD800
            lowSurrogate = (codePoint % 0x400) + 0xDC00
            codeUnits.push(highSurrogate, lowSurrogate)
          }
          if (index + 1 === length || codeUnits.length > MAX_SIZE) {
            result += stringFromCharCode.apply(null, codeUnits)
            codeUnits.length = 0
          }
        }
        return result
      }
      /* istanbul ignore next */
      if (Object.defineProperty) {
        Object.defineProperty(String, 'fromCodePoint', {
          value: fromCodePoint,
          configurable: true,
          writable: true
        })
      } else {
        String.fromCodePoint = fromCodePoint
      }
    }())
  }
})( false ? 0 : exports)


/***/ }),

/***/ 86255:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var GetIntrinsic = __webpack_require__(28651);
var define = __webpack_require__(89295);
var hasDescriptors = __webpack_require__(18890)();
var gOPD = __webpack_require__(68109);

var $TypeError = __webpack_require__(96785);
var $floor = GetIntrinsic('%Math.floor%');

/** @type {import('.')} */
module.exports = function setFunctionLength(fn, length) {
	if (typeof fn !== 'function') {
		throw new $TypeError('`fn` is not a function');
	}
	if (typeof length !== 'number' || length < 0 || length > 0xFFFFFFFF || $floor(length) !== length) {
		throw new $TypeError('`length` must be a positive 32-bit integer');
	}

	var loose = arguments.length > 2 && !!arguments[2];

	var functionLengthIsConfigurable = true;
	var functionLengthIsWritable = true;
	if ('length' in fn && gOPD) {
		var desc = gOPD(fn, 'length');
		if (desc && !desc.configurable) {
			functionLengthIsConfigurable = false;
		}
		if (desc && !desc.writable) {
			functionLengthIsWritable = false;
		}
	}

	if (functionLengthIsConfigurable || functionLengthIsWritable || !loose) {
		if (hasDescriptors) {
			define(/** @type {Parameters<define>[0]} */ (fn), 'length', length, true, true);
		} else {
			define(/** @type {Parameters<define>[0]} */ (fn), 'length', length);
		}
	}
	return fn;
};


/***/ }),

/***/ 95304:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var define = __webpack_require__(89295);
var hasDescriptors = __webpack_require__(18890)();
var functionsHaveConfigurableNames = (__webpack_require__(61084).functionsHaveConfigurableNames)();

var $TypeError = __webpack_require__(96785);

/** @type {import('.')} */
module.exports = function setFunctionName(fn, name) {
	if (typeof fn !== 'function') {
		throw new $TypeError('`fn` is not a function');
	}
	var loose = arguments.length > 2 && !!arguments[2];
	if (!loose || functionsHaveConfigurableNames) {
		if (hasDescriptors) {
			define(/** @type {Parameters<define>[0]} */ (fn), 'name', name, true, true);
		} else {
			define(/** @type {Parameters<define>[0]} */ (fn), 'name', name);
		}
	}
	return fn;
};


/***/ }),

/***/ 9760:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

module.exports = Stream;

var EE = (__webpack_require__(64785).EventEmitter);
var inherits = __webpack_require__(89784);

inherits(Stream, EE);
Stream.Readable = __webpack_require__(88261);
Stream.Writable = __webpack_require__(29781);
Stream.Duplex = __webpack_require__(14903);
Stream.Transform = __webpack_require__(48569);
Stream.PassThrough = __webpack_require__(17723);
Stream.finished = __webpack_require__(12167)
Stream.pipeline = __webpack_require__(43765)

// Backwards-compat with node 0.4.x
Stream.Stream = Stream;



// old-style streams.  Note that the pipe method (the only relevant
// part of this class) is overridden in the Readable class.

function Stream() {
  EE.call(this);
}

Stream.prototype.pipe = function(dest, options) {
  var source = this;

  function ondata(chunk) {
    if (dest.writable) {
      if (false === dest.write(chunk) && source.pause) {
        source.pause();
      }
    }
  }

  source.on('data', ondata);

  function ondrain() {
    if (source.readable && source.resume) {
      source.resume();
    }
  }

  dest.on('drain', ondrain);

  // If the 'end' option is not supplied, dest.end() will be called when
  // source gets the 'end' or 'close' events.  Only dest.end() once.
  if (!dest._isStdio && (!options || options.end !== false)) {
    source.on('end', onend);
    source.on('close', onclose);
  }

  var didOnEnd = false;
  function onend() {
    if (didOnEnd) return;
    didOnEnd = true;

    dest.end();
  }


  function onclose() {
    if (didOnEnd) return;
    didOnEnd = true;

    if (typeof dest.destroy === 'function') dest.destroy();
  }

  // don't leave dangling pipes when there are errors.
  function onerror(er) {
    cleanup();
    if (EE.listenerCount(this, 'error') === 0) {
      throw er; // Unhandled stream error in pipe.
    }
  }

  source.on('error', onerror);
  dest.on('error', onerror);

  // remove all the event listeners that were added.
  function cleanup() {
    source.removeListener('data', ondata);
    dest.removeListener('drain', ondrain);

    source.removeListener('end', onend);
    source.removeListener('close', onclose);

    source.removeListener('error', onerror);
    dest.removeListener('error', onerror);

    source.removeListener('end', cleanup);
    source.removeListener('close', cleanup);

    dest.removeListener('close', cleanup);
  }

  source.on('end', cleanup);
  source.on('close', cleanup);

  dest.on('close', cleanup);

  dest.emit('pipe', source);

  // Allow for unix-like usage: A.pipe(B).pipe(C)
  return dest;
};


/***/ }),

/***/ 83797:
/***/ (function(module) {

"use strict";


function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; subClass.__proto__ = superClass; }

var codes = {};

function createErrorType(code, message, Base) {
  if (!Base) {
    Base = Error;
  }

  function getMessage(arg1, arg2, arg3) {
    if (typeof message === 'string') {
      return message;
    } else {
      return message(arg1, arg2, arg3);
    }
  }

  var NodeError =
  /*#__PURE__*/
  function (_Base) {
    _inheritsLoose(NodeError, _Base);

    function NodeError(arg1, arg2, arg3) {
      return _Base.call(this, getMessage(arg1, arg2, arg3)) || this;
    }

    return NodeError;
  }(Base);

  NodeError.prototype.name = Base.name;
  NodeError.prototype.code = code;
  codes[code] = NodeError;
} // https://github.com/nodejs/node/blob/v10.8.0/lib/internal/errors.js


function oneOf(expected, thing) {
  if (Array.isArray(expected)) {
    var len = expected.length;
    expected = expected.map(function (i) {
      return String(i);
    });

    if (len > 2) {
      return "one of ".concat(thing, " ").concat(expected.slice(0, len - 1).join(', '), ", or ") + expected[len - 1];
    } else if (len === 2) {
      return "one of ".concat(thing, " ").concat(expected[0], " or ").concat(expected[1]);
    } else {
      return "of ".concat(thing, " ").concat(expected[0]);
    }
  } else {
    return "of ".concat(thing, " ").concat(String(expected));
  }
} // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/startsWith


function startsWith(str, search, pos) {
  return str.substr(!pos || pos < 0 ? 0 : +pos, search.length) === search;
} // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/endsWith


function endsWith(str, search, this_len) {
  if (this_len === undefined || this_len > str.length) {
    this_len = str.length;
  }

  return str.substring(this_len - search.length, this_len) === search;
} // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes


function includes(str, search, start) {
  if (typeof start !== 'number') {
    start = 0;
  }

  if (start + search.length > str.length) {
    return false;
  } else {
    return str.indexOf(search, start) !== -1;
  }
}

createErrorType('ERR_INVALID_OPT_VALUE', function (name, value) {
  return 'The value "' + value + '" is invalid for option "' + name + '"';
}, TypeError);
createErrorType('ERR_INVALID_ARG_TYPE', function (name, expected, actual) {
  // determiner: 'must be' or 'must not be'
  var determiner;

  if (typeof expected === 'string' && startsWith(expected, 'not ')) {
    determiner = 'must not be';
    expected = expected.replace(/^not /, '');
  } else {
    determiner = 'must be';
  }

  var msg;

  if (endsWith(name, ' argument')) {
    // For cases like 'first argument'
    msg = "The ".concat(name, " ").concat(determiner, " ").concat(oneOf(expected, 'type'));
  } else {
    var type = includes(name, '.') ? 'property' : 'argument';
    msg = "The \"".concat(name, "\" ").concat(type, " ").concat(determiner, " ").concat(oneOf(expected, 'type'));
  }

  msg += ". Received type ".concat(typeof actual);
  return msg;
}, TypeError);
createErrorType('ERR_STREAM_PUSH_AFTER_EOF', 'stream.push() after EOF');
createErrorType('ERR_METHOD_NOT_IMPLEMENTED', function (name) {
  return 'The ' + name + ' method is not implemented';
});
createErrorType('ERR_STREAM_PREMATURE_CLOSE', 'Premature close');
createErrorType('ERR_STREAM_DESTROYED', function (name) {
  return 'Cannot call ' + name + ' after a stream was destroyed';
});
createErrorType('ERR_MULTIPLE_CALLBACK', 'Callback called multiple times');
createErrorType('ERR_STREAM_CANNOT_PIPE', 'Cannot pipe, not readable');
createErrorType('ERR_STREAM_WRITE_AFTER_END', 'write after end');
createErrorType('ERR_STREAM_NULL_VALUES', 'May not write null values to stream', TypeError);
createErrorType('ERR_UNKNOWN_ENCODING', function (arg) {
  return 'Unknown encoding: ' + arg;
}, TypeError);
createErrorType('ERR_STREAM_UNSHIFT_AFTER_END_EVENT', 'stream.unshift() after end event');
module.exports.F = codes;


/***/ }),

/***/ 14903:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a duplex stream is just a stream that is both readable and writable.
// Since JS doesn't have multiple prototypal inheritance, this class
// prototypally inherits from Readable, and then parasitically from
// Writable.



/*<replacement>*/
var objectKeys = Object.keys || function (obj) {
  var keys = [];
  for (var key in obj) keys.push(key);
  return keys;
};
/*</replacement>*/

module.exports = Duplex;
var Readable = __webpack_require__(88261);
var Writable = __webpack_require__(29781);
__webpack_require__(89784)(Duplex, Readable);
{
  // Allow the keys array to be GC'ed.
  var keys = objectKeys(Writable.prototype);
  for (var v = 0; v < keys.length; v++) {
    var method = keys[v];
    if (!Duplex.prototype[method]) Duplex.prototype[method] = Writable.prototype[method];
  }
}
function Duplex(options) {
  if (!(this instanceof Duplex)) return new Duplex(options);
  Readable.call(this, options);
  Writable.call(this, options);
  this.allowHalfOpen = true;
  if (options) {
    if (options.readable === false) this.readable = false;
    if (options.writable === false) this.writable = false;
    if (options.allowHalfOpen === false) {
      this.allowHalfOpen = false;
      this.once('end', onend);
    }
  }
}
Object.defineProperty(Duplex.prototype, 'writableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState.highWaterMark;
  }
});
Object.defineProperty(Duplex.prototype, 'writableBuffer', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState && this._writableState.getBuffer();
  }
});
Object.defineProperty(Duplex.prototype, 'writableLength', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState.length;
  }
});

// the no-half-open enforcer
function onend() {
  // If the writable side ended, then we're ok.
  if (this._writableState.ended) return;

  // no more data can be written.
  // But allow more writes to happen in this tick.
  process.nextTick(onEndNT, this);
}
function onEndNT(self) {
  self.end();
}
Object.defineProperty(Duplex.prototype, 'destroyed', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    if (this._readableState === undefined || this._writableState === undefined) {
      return false;
    }
    return this._readableState.destroyed && this._writableState.destroyed;
  },
  set: function set(value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (this._readableState === undefined || this._writableState === undefined) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._readableState.destroyed = value;
    this._writableState.destroyed = value;
  }
});

/***/ }),

/***/ 17723:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a passthrough stream.
// basically just the most minimal sort of Transform stream.
// Every written chunk gets output as-is.



module.exports = PassThrough;
var Transform = __webpack_require__(48569);
__webpack_require__(89784)(PassThrough, Transform);
function PassThrough(options) {
  if (!(this instanceof PassThrough)) return new PassThrough(options);
  Transform.call(this, options);
}
PassThrough.prototype._transform = function (chunk, encoding, cb) {
  cb(null, chunk);
};

/***/ }),

/***/ 88261:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



module.exports = Readable;

/*<replacement>*/
var Duplex;
/*</replacement>*/

Readable.ReadableState = ReadableState;

/*<replacement>*/
var EE = (__webpack_require__(64785).EventEmitter);
var EElistenerCount = function EElistenerCount(emitter, type) {
  return emitter.listeners(type).length;
};
/*</replacement>*/

/*<replacement>*/
var Stream = __webpack_require__(99018);
/*</replacement>*/

var Buffer = (__webpack_require__(14598).Buffer);
var OurUint8Array = (typeof __webpack_require__.g !== 'undefined' ? __webpack_require__.g : typeof window !== 'undefined' ? window : typeof self !== 'undefined' ? self : {}).Uint8Array || function () {};
function _uint8ArrayToBuffer(chunk) {
  return Buffer.from(chunk);
}
function _isUint8Array(obj) {
  return Buffer.isBuffer(obj) || obj instanceof OurUint8Array;
}

/*<replacement>*/
var debugUtil = __webpack_require__(77199);
var debug;
if (debugUtil && debugUtil.debuglog) {
  debug = debugUtil.debuglog('stream');
} else {
  debug = function debug() {};
}
/*</replacement>*/

var BufferList = __webpack_require__(24433);
var destroyImpl = __webpack_require__(37385);
var _require = __webpack_require__(68130),
  getHighWaterMark = _require.getHighWaterMark;
var _require$codes = (__webpack_require__(83797)/* .codes */ .F),
  ERR_INVALID_ARG_TYPE = _require$codes.ERR_INVALID_ARG_TYPE,
  ERR_STREAM_PUSH_AFTER_EOF = _require$codes.ERR_STREAM_PUSH_AFTER_EOF,
  ERR_METHOD_NOT_IMPLEMENTED = _require$codes.ERR_METHOD_NOT_IMPLEMENTED,
  ERR_STREAM_UNSHIFT_AFTER_END_EVENT = _require$codes.ERR_STREAM_UNSHIFT_AFTER_END_EVENT;

// Lazy loaded to improve the startup performance.
var StringDecoder;
var createReadableStreamAsyncIterator;
var from;
__webpack_require__(89784)(Readable, Stream);
var errorOrDestroy = destroyImpl.errorOrDestroy;
var kProxyEvents = ['error', 'close', 'destroy', 'pause', 'resume'];
function prependListener(emitter, event, fn) {
  // Sadly this is not cacheable as some libraries bundle their own
  // event emitter implementation with them.
  if (typeof emitter.prependListener === 'function') return emitter.prependListener(event, fn);

  // This is a hack to make sure that our error handler is attached before any
  // userland ones.  NEVER DO THIS. This is here only because this code needs
  // to continue to work with older versions of Node.js that do not include
  // the prependListener() method. The goal is to eventually remove this hack.
  if (!emitter._events || !emitter._events[event]) emitter.on(event, fn);else if (Array.isArray(emitter._events[event])) emitter._events[event].unshift(fn);else emitter._events[event] = [fn, emitter._events[event]];
}
function ReadableState(options, stream, isDuplex) {
  Duplex = Duplex || __webpack_require__(14903);
  options = options || {};

  // Duplex streams are both readable and writable, but share
  // the same options object.
  // However, some cases require setting options to different
  // values for the readable and the writable sides of the duplex stream.
  // These options can be provided separately as readableXXX and writableXXX.
  if (typeof isDuplex !== 'boolean') isDuplex = stream instanceof Duplex;

  // object stream flag. Used to make read(n) ignore n and to
  // make all the buffer merging and length checks go away
  this.objectMode = !!options.objectMode;
  if (isDuplex) this.objectMode = this.objectMode || !!options.readableObjectMode;

  // the point at which it stops calling _read() to fill the buffer
  // Note: 0 is a valid value, means "don't call _read preemptively ever"
  this.highWaterMark = getHighWaterMark(this, options, 'readableHighWaterMark', isDuplex);

  // A linked list is used to store data chunks instead of an array because the
  // linked list can remove elements from the beginning faster than
  // array.shift()
  this.buffer = new BufferList();
  this.length = 0;
  this.pipes = null;
  this.pipesCount = 0;
  this.flowing = null;
  this.ended = false;
  this.endEmitted = false;
  this.reading = false;

  // a flag to be able to tell if the event 'readable'/'data' is emitted
  // immediately, or on a later tick.  We set this to true at first, because
  // any actions that shouldn't happen until "later" should generally also
  // not happen before the first read call.
  this.sync = true;

  // whenever we return null, then we set a flag to say
  // that we're awaiting a 'readable' event emission.
  this.needReadable = false;
  this.emittedReadable = false;
  this.readableListening = false;
  this.resumeScheduled = false;
  this.paused = true;

  // Should close be emitted on destroy. Defaults to true.
  this.emitClose = options.emitClose !== false;

  // Should .destroy() be called after 'end' (and potentially 'finish')
  this.autoDestroy = !!options.autoDestroy;

  // has it been destroyed
  this.destroyed = false;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // the number of writers that are awaiting a drain event in .pipe()s
  this.awaitDrain = 0;

  // if true, a maybeReadMore has been scheduled
  this.readingMore = false;
  this.decoder = null;
  this.encoding = null;
  if (options.encoding) {
    if (!StringDecoder) StringDecoder = (__webpack_require__(43143)/* .StringDecoder */ .I);
    this.decoder = new StringDecoder(options.encoding);
    this.encoding = options.encoding;
  }
}
function Readable(options) {
  Duplex = Duplex || __webpack_require__(14903);
  if (!(this instanceof Readable)) return new Readable(options);

  // Checking for a Stream.Duplex instance is faster here instead of inside
  // the ReadableState constructor, at least with V8 6.5
  var isDuplex = this instanceof Duplex;
  this._readableState = new ReadableState(options, this, isDuplex);

  // legacy
  this.readable = true;
  if (options) {
    if (typeof options.read === 'function') this._read = options.read;
    if (typeof options.destroy === 'function') this._destroy = options.destroy;
  }
  Stream.call(this);
}
Object.defineProperty(Readable.prototype, 'destroyed', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    if (this._readableState === undefined) {
      return false;
    }
    return this._readableState.destroyed;
  },
  set: function set(value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (!this._readableState) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._readableState.destroyed = value;
  }
});
Readable.prototype.destroy = destroyImpl.destroy;
Readable.prototype._undestroy = destroyImpl.undestroy;
Readable.prototype._destroy = function (err, cb) {
  cb(err);
};

// Manually shove something into the read() buffer.
// This returns true if the highWaterMark has not been hit yet,
// similar to how Writable.write() returns true if you should
// write() some more.
Readable.prototype.push = function (chunk, encoding) {
  var state = this._readableState;
  var skipChunkCheck;
  if (!state.objectMode) {
    if (typeof chunk === 'string') {
      encoding = encoding || state.defaultEncoding;
      if (encoding !== state.encoding) {
        chunk = Buffer.from(chunk, encoding);
        encoding = '';
      }
      skipChunkCheck = true;
    }
  } else {
    skipChunkCheck = true;
  }
  return readableAddChunk(this, chunk, encoding, false, skipChunkCheck);
};

// Unshift should *always* be something directly out of read()
Readable.prototype.unshift = function (chunk) {
  return readableAddChunk(this, chunk, null, true, false);
};
function readableAddChunk(stream, chunk, encoding, addToFront, skipChunkCheck) {
  debug('readableAddChunk', chunk);
  var state = stream._readableState;
  if (chunk === null) {
    state.reading = false;
    onEofChunk(stream, state);
  } else {
    var er;
    if (!skipChunkCheck) er = chunkInvalid(state, chunk);
    if (er) {
      errorOrDestroy(stream, er);
    } else if (state.objectMode || chunk && chunk.length > 0) {
      if (typeof chunk !== 'string' && !state.objectMode && Object.getPrototypeOf(chunk) !== Buffer.prototype) {
        chunk = _uint8ArrayToBuffer(chunk);
      }
      if (addToFront) {
        if (state.endEmitted) errorOrDestroy(stream, new ERR_STREAM_UNSHIFT_AFTER_END_EVENT());else addChunk(stream, state, chunk, true);
      } else if (state.ended) {
        errorOrDestroy(stream, new ERR_STREAM_PUSH_AFTER_EOF());
      } else if (state.destroyed) {
        return false;
      } else {
        state.reading = false;
        if (state.decoder && !encoding) {
          chunk = state.decoder.write(chunk);
          if (state.objectMode || chunk.length !== 0) addChunk(stream, state, chunk, false);else maybeReadMore(stream, state);
        } else {
          addChunk(stream, state, chunk, false);
        }
      }
    } else if (!addToFront) {
      state.reading = false;
      maybeReadMore(stream, state);
    }
  }

  // We can push more data if we are below the highWaterMark.
  // Also, if we have no data yet, we can stand some more bytes.
  // This is to work around cases where hwm=0, such as the repl.
  return !state.ended && (state.length < state.highWaterMark || state.length === 0);
}
function addChunk(stream, state, chunk, addToFront) {
  if (state.flowing && state.length === 0 && !state.sync) {
    state.awaitDrain = 0;
    stream.emit('data', chunk);
  } else {
    // update the buffer info.
    state.length += state.objectMode ? 1 : chunk.length;
    if (addToFront) state.buffer.unshift(chunk);else state.buffer.push(chunk);
    if (state.needReadable) emitReadable(stream);
  }
  maybeReadMore(stream, state);
}
function chunkInvalid(state, chunk) {
  var er;
  if (!_isUint8Array(chunk) && typeof chunk !== 'string' && chunk !== undefined && !state.objectMode) {
    er = new ERR_INVALID_ARG_TYPE('chunk', ['string', 'Buffer', 'Uint8Array'], chunk);
  }
  return er;
}
Readable.prototype.isPaused = function () {
  return this._readableState.flowing === false;
};

// backwards compatibility.
Readable.prototype.setEncoding = function (enc) {
  if (!StringDecoder) StringDecoder = (__webpack_require__(43143)/* .StringDecoder */ .I);
  var decoder = new StringDecoder(enc);
  this._readableState.decoder = decoder;
  // If setEncoding(null), decoder.encoding equals utf8
  this._readableState.encoding = this._readableState.decoder.encoding;

  // Iterate over current buffer to convert already stored Buffers:
  var p = this._readableState.buffer.head;
  var content = '';
  while (p !== null) {
    content += decoder.write(p.data);
    p = p.next;
  }
  this._readableState.buffer.clear();
  if (content !== '') this._readableState.buffer.push(content);
  this._readableState.length = content.length;
  return this;
};

// Don't raise the hwm > 1GB
var MAX_HWM = 0x40000000;
function computeNewHighWaterMark(n) {
  if (n >= MAX_HWM) {
    // TODO(ronag): Throw ERR_VALUE_OUT_OF_RANGE.
    n = MAX_HWM;
  } else {
    // Get the next highest power of 2 to prevent increasing hwm excessively in
    // tiny amounts
    n--;
    n |= n >>> 1;
    n |= n >>> 2;
    n |= n >>> 4;
    n |= n >>> 8;
    n |= n >>> 16;
    n++;
  }
  return n;
}

// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function howMuchToRead(n, state) {
  if (n <= 0 || state.length === 0 && state.ended) return 0;
  if (state.objectMode) return 1;
  if (n !== n) {
    // Only flow one buffer at a time
    if (state.flowing && state.length) return state.buffer.head.data.length;else return state.length;
  }
  // If we're asking for more than the current hwm, then raise the hwm.
  if (n > state.highWaterMark) state.highWaterMark = computeNewHighWaterMark(n);
  if (n <= state.length) return n;
  // Don't have enough
  if (!state.ended) {
    state.needReadable = true;
    return 0;
  }
  return state.length;
}

// you can override either this method, or the async _read(n) below.
Readable.prototype.read = function (n) {
  debug('read', n);
  n = parseInt(n, 10);
  var state = this._readableState;
  var nOrig = n;
  if (n !== 0) state.emittedReadable = false;

  // if we're doing read(0) to trigger a readable event, but we
  // already have a bunch of data in the buffer, then just trigger
  // the 'readable' event and move on.
  if (n === 0 && state.needReadable && ((state.highWaterMark !== 0 ? state.length >= state.highWaterMark : state.length > 0) || state.ended)) {
    debug('read: emitReadable', state.length, state.ended);
    if (state.length === 0 && state.ended) endReadable(this);else emitReadable(this);
    return null;
  }
  n = howMuchToRead(n, state);

  // if we've ended, and we're now clear, then finish it up.
  if (n === 0 && state.ended) {
    if (state.length === 0) endReadable(this);
    return null;
  }

  // All the actual chunk generation logic needs to be
  // *below* the call to _read.  The reason is that in certain
  // synthetic stream cases, such as passthrough streams, _read
  // may be a completely synchronous operation which may change
  // the state of the read buffer, providing enough data when
  // before there was *not* enough.
  //
  // So, the steps are:
  // 1. Figure out what the state of things will be after we do
  // a read from the buffer.
  //
  // 2. If that resulting state will trigger a _read, then call _read.
  // Note that this may be asynchronous, or synchronous.  Yes, it is
  // deeply ugly to write APIs this way, but that still doesn't mean
  // that the Readable class should behave improperly, as streams are
  // designed to be sync/async agnostic.
  // Take note if the _read call is sync or async (ie, if the read call
  // has returned yet), so that we know whether or not it's safe to emit
  // 'readable' etc.
  //
  // 3. Actually pull the requested chunks out of the buffer and return.

  // if we need a readable event, then we need to do some reading.
  var doRead = state.needReadable;
  debug('need readable', doRead);

  // if we currently have less than the highWaterMark, then also read some
  if (state.length === 0 || state.length - n < state.highWaterMark) {
    doRead = true;
    debug('length less than watermark', doRead);
  }

  // however, if we've ended, then there's no point, and if we're already
  // reading, then it's unnecessary.
  if (state.ended || state.reading) {
    doRead = false;
    debug('reading or ended', doRead);
  } else if (doRead) {
    debug('do read');
    state.reading = true;
    state.sync = true;
    // if the length is currently zero, then we *need* a readable event.
    if (state.length === 0) state.needReadable = true;
    // call internal read method
    this._read(state.highWaterMark);
    state.sync = false;
    // If _read pushed data synchronously, then `reading` will be false,
    // and we need to re-evaluate how much data we can return to the user.
    if (!state.reading) n = howMuchToRead(nOrig, state);
  }
  var ret;
  if (n > 0) ret = fromList(n, state);else ret = null;
  if (ret === null) {
    state.needReadable = state.length <= state.highWaterMark;
    n = 0;
  } else {
    state.length -= n;
    state.awaitDrain = 0;
  }
  if (state.length === 0) {
    // If we have nothing in the buffer, then we want to know
    // as soon as we *do* get something into the buffer.
    if (!state.ended) state.needReadable = true;

    // If we tried to read() past the EOF, then emit end on the next tick.
    if (nOrig !== n && state.ended) endReadable(this);
  }
  if (ret !== null) this.emit('data', ret);
  return ret;
};
function onEofChunk(stream, state) {
  debug('onEofChunk');
  if (state.ended) return;
  if (state.decoder) {
    var chunk = state.decoder.end();
    if (chunk && chunk.length) {
      state.buffer.push(chunk);
      state.length += state.objectMode ? 1 : chunk.length;
    }
  }
  state.ended = true;
  if (state.sync) {
    // if we are sync, wait until next tick to emit the data.
    // Otherwise we risk emitting data in the flow()
    // the readable code triggers during a read() call
    emitReadable(stream);
  } else {
    // emit 'readable' now to make sure it gets picked up.
    state.needReadable = false;
    if (!state.emittedReadable) {
      state.emittedReadable = true;
      emitReadable_(stream);
    }
  }
}

// Don't emit readable right away in sync mode, because this can trigger
// another read() call => stack overflow.  This way, it might trigger
// a nextTick recursion warning, but that's not so bad.
function emitReadable(stream) {
  var state = stream._readableState;
  debug('emitReadable', state.needReadable, state.emittedReadable);
  state.needReadable = false;
  if (!state.emittedReadable) {
    debug('emitReadable', state.flowing);
    state.emittedReadable = true;
    process.nextTick(emitReadable_, stream);
  }
}
function emitReadable_(stream) {
  var state = stream._readableState;
  debug('emitReadable_', state.destroyed, state.length, state.ended);
  if (!state.destroyed && (state.length || state.ended)) {
    stream.emit('readable');
    state.emittedReadable = false;
  }

  // The stream needs another readable event if
  // 1. It is not flowing, as the flow mechanism will take
  //    care of it.
  // 2. It is not ended.
  // 3. It is below the highWaterMark, so we can schedule
  //    another readable later.
  state.needReadable = !state.flowing && !state.ended && state.length <= state.highWaterMark;
  flow(stream);
}

// at this point, the user has presumably seen the 'readable' event,
// and called read() to consume some data.  that may have triggered
// in turn another _read(n) call, in which case reading = true if
// it's in progress.
// However, if we're not ended, or reading, and the length < hwm,
// then go ahead and try to read some more preemptively.
function maybeReadMore(stream, state) {
  if (!state.readingMore) {
    state.readingMore = true;
    process.nextTick(maybeReadMore_, stream, state);
  }
}
function maybeReadMore_(stream, state) {
  // Attempt to read more data if we should.
  //
  // The conditions for reading more data are (one of):
  // - Not enough data buffered (state.length < state.highWaterMark). The loop
  //   is responsible for filling the buffer with enough data if such data
  //   is available. If highWaterMark is 0 and we are not in the flowing mode
  //   we should _not_ attempt to buffer any extra data. We'll get more data
  //   when the stream consumer calls read() instead.
  // - No data in the buffer, and the stream is in flowing mode. In this mode
  //   the loop below is responsible for ensuring read() is called. Failing to
  //   call read here would abort the flow and there's no other mechanism for
  //   continuing the flow if the stream consumer has just subscribed to the
  //   'data' event.
  //
  // In addition to the above conditions to keep reading data, the following
  // conditions prevent the data from being read:
  // - The stream has ended (state.ended).
  // - There is already a pending 'read' operation (state.reading). This is a
  //   case where the the stream has called the implementation defined _read()
  //   method, but they are processing the call asynchronously and have _not_
  //   called push() with new data. In this case we skip performing more
  //   read()s. The execution ends in this method again after the _read() ends
  //   up calling push() with more data.
  while (!state.reading && !state.ended && (state.length < state.highWaterMark || state.flowing && state.length === 0)) {
    var len = state.length;
    debug('maybeReadMore read 0');
    stream.read(0);
    if (len === state.length)
      // didn't get any data, stop spinning.
      break;
  }
  state.readingMore = false;
}

// abstract method.  to be overridden in specific implementation classes.
// call cb(er, data) where data is <= n in length.
// for virtual (non-string, non-buffer) streams, "length" is somewhat
// arbitrary, and perhaps not very meaningful.
Readable.prototype._read = function (n) {
  errorOrDestroy(this, new ERR_METHOD_NOT_IMPLEMENTED('_read()'));
};
Readable.prototype.pipe = function (dest, pipeOpts) {
  var src = this;
  var state = this._readableState;
  switch (state.pipesCount) {
    case 0:
      state.pipes = dest;
      break;
    case 1:
      state.pipes = [state.pipes, dest];
      break;
    default:
      state.pipes.push(dest);
      break;
  }
  state.pipesCount += 1;
  debug('pipe count=%d opts=%j', state.pipesCount, pipeOpts);
  var doEnd = (!pipeOpts || pipeOpts.end !== false) && dest !== process.stdout && dest !== process.stderr;
  var endFn = doEnd ? onend : unpipe;
  if (state.endEmitted) process.nextTick(endFn);else src.once('end', endFn);
  dest.on('unpipe', onunpipe);
  function onunpipe(readable, unpipeInfo) {
    debug('onunpipe');
    if (readable === src) {
      if (unpipeInfo && unpipeInfo.hasUnpiped === false) {
        unpipeInfo.hasUnpiped = true;
        cleanup();
      }
    }
  }
  function onend() {
    debug('onend');
    dest.end();
  }

  // when the dest drains, it reduces the awaitDrain counter
  // on the source.  This would be more elegant with a .once()
  // handler in flow(), but adding and removing repeatedly is
  // too slow.
  var ondrain = pipeOnDrain(src);
  dest.on('drain', ondrain);
  var cleanedUp = false;
  function cleanup() {
    debug('cleanup');
    // cleanup event handlers once the pipe is broken
    dest.removeListener('close', onclose);
    dest.removeListener('finish', onfinish);
    dest.removeListener('drain', ondrain);
    dest.removeListener('error', onerror);
    dest.removeListener('unpipe', onunpipe);
    src.removeListener('end', onend);
    src.removeListener('end', unpipe);
    src.removeListener('data', ondata);
    cleanedUp = true;

    // if the reader is waiting for a drain event from this
    // specific writer, then it would cause it to never start
    // flowing again.
    // So, if this is awaiting a drain, then we just call it now.
    // If we don't know, then assume that we are waiting for one.
    if (state.awaitDrain && (!dest._writableState || dest._writableState.needDrain)) ondrain();
  }
  src.on('data', ondata);
  function ondata(chunk) {
    debug('ondata');
    var ret = dest.write(chunk);
    debug('dest.write', ret);
    if (ret === false) {
      // If the user unpiped during `dest.write()`, it is possible
      // to get stuck in a permanently paused state if that write
      // also returned false.
      // => Check whether `dest` is still a piping destination.
      if ((state.pipesCount === 1 && state.pipes === dest || state.pipesCount > 1 && indexOf(state.pipes, dest) !== -1) && !cleanedUp) {
        debug('false write response, pause', state.awaitDrain);
        state.awaitDrain++;
      }
      src.pause();
    }
  }

  // if the dest has an error, then stop piping into it.
  // however, don't suppress the throwing behavior for this.
  function onerror(er) {
    debug('onerror', er);
    unpipe();
    dest.removeListener('error', onerror);
    if (EElistenerCount(dest, 'error') === 0) errorOrDestroy(dest, er);
  }

  // Make sure our error handler is attached before userland ones.
  prependListener(dest, 'error', onerror);

  // Both close and finish should trigger unpipe, but only once.
  function onclose() {
    dest.removeListener('finish', onfinish);
    unpipe();
  }
  dest.once('close', onclose);
  function onfinish() {
    debug('onfinish');
    dest.removeListener('close', onclose);
    unpipe();
  }
  dest.once('finish', onfinish);
  function unpipe() {
    debug('unpipe');
    src.unpipe(dest);
  }

  // tell the dest that it's being piped to
  dest.emit('pipe', src);

  // start the flow if it hasn't been started already.
  if (!state.flowing) {
    debug('pipe resume');
    src.resume();
  }
  return dest;
};
function pipeOnDrain(src) {
  return function pipeOnDrainFunctionResult() {
    var state = src._readableState;
    debug('pipeOnDrain', state.awaitDrain);
    if (state.awaitDrain) state.awaitDrain--;
    if (state.awaitDrain === 0 && EElistenerCount(src, 'data')) {
      state.flowing = true;
      flow(src);
    }
  };
}
Readable.prototype.unpipe = function (dest) {
  var state = this._readableState;
  var unpipeInfo = {
    hasUnpiped: false
  };

  // if we're not piping anywhere, then do nothing.
  if (state.pipesCount === 0) return this;

  // just one destination.  most common case.
  if (state.pipesCount === 1) {
    // passed in one, but it's not the right one.
    if (dest && dest !== state.pipes) return this;
    if (!dest) dest = state.pipes;

    // got a match.
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;
    if (dest) dest.emit('unpipe', this, unpipeInfo);
    return this;
  }

  // slow case. multiple pipe destinations.

  if (!dest) {
    // remove all.
    var dests = state.pipes;
    var len = state.pipesCount;
    state.pipes = null;
    state.pipesCount = 0;
    state.flowing = false;
    for (var i = 0; i < len; i++) dests[i].emit('unpipe', this, {
      hasUnpiped: false
    });
    return this;
  }

  // try to find the right one.
  var index = indexOf(state.pipes, dest);
  if (index === -1) return this;
  state.pipes.splice(index, 1);
  state.pipesCount -= 1;
  if (state.pipesCount === 1) state.pipes = state.pipes[0];
  dest.emit('unpipe', this, unpipeInfo);
  return this;
};

// set up data events if they are asked for
// Ensure readable listeners eventually get something
Readable.prototype.on = function (ev, fn) {
  var res = Stream.prototype.on.call(this, ev, fn);
  var state = this._readableState;
  if (ev === 'data') {
    // update readableListening so that resume() may be a no-op
    // a few lines down. This is needed to support once('readable').
    state.readableListening = this.listenerCount('readable') > 0;

    // Try start flowing on next tick if stream isn't explicitly paused
    if (state.flowing !== false) this.resume();
  } else if (ev === 'readable') {
    if (!state.endEmitted && !state.readableListening) {
      state.readableListening = state.needReadable = true;
      state.flowing = false;
      state.emittedReadable = false;
      debug('on readable', state.length, state.reading);
      if (state.length) {
        emitReadable(this);
      } else if (!state.reading) {
        process.nextTick(nReadingNextTick, this);
      }
    }
  }
  return res;
};
Readable.prototype.addListener = Readable.prototype.on;
Readable.prototype.removeListener = function (ev, fn) {
  var res = Stream.prototype.removeListener.call(this, ev, fn);
  if (ev === 'readable') {
    // We need to check if there is someone still listening to
    // readable and reset the state. However this needs to happen
    // after readable has been emitted but before I/O (nextTick) to
    // support once('readable', fn) cycles. This means that calling
    // resume within the same tick will have no
    // effect.
    process.nextTick(updateReadableListening, this);
  }
  return res;
};
Readable.prototype.removeAllListeners = function (ev) {
  var res = Stream.prototype.removeAllListeners.apply(this, arguments);
  if (ev === 'readable' || ev === undefined) {
    // We need to check if there is someone still listening to
    // readable and reset the state. However this needs to happen
    // after readable has been emitted but before I/O (nextTick) to
    // support once('readable', fn) cycles. This means that calling
    // resume within the same tick will have no
    // effect.
    process.nextTick(updateReadableListening, this);
  }
  return res;
};
function updateReadableListening(self) {
  var state = self._readableState;
  state.readableListening = self.listenerCount('readable') > 0;
  if (state.resumeScheduled && !state.paused) {
    // flowing needs to be set to true now, otherwise
    // the upcoming resume will not flow.
    state.flowing = true;

    // crude way to check if we should resume
  } else if (self.listenerCount('data') > 0) {
    self.resume();
  }
}
function nReadingNextTick(self) {
  debug('readable nexttick read 0');
  self.read(0);
}

// pause() and resume() are remnants of the legacy readable stream API
// If the user uses them, then switch into old mode.
Readable.prototype.resume = function () {
  var state = this._readableState;
  if (!state.flowing) {
    debug('resume');
    // we flow only if there is no one listening
    // for readable, but we still have to call
    // resume()
    state.flowing = !state.readableListening;
    resume(this, state);
  }
  state.paused = false;
  return this;
};
function resume(stream, state) {
  if (!state.resumeScheduled) {
    state.resumeScheduled = true;
    process.nextTick(resume_, stream, state);
  }
}
function resume_(stream, state) {
  debug('resume', state.reading);
  if (!state.reading) {
    stream.read(0);
  }
  state.resumeScheduled = false;
  stream.emit('resume');
  flow(stream);
  if (state.flowing && !state.reading) stream.read(0);
}
Readable.prototype.pause = function () {
  debug('call pause flowing=%j', this._readableState.flowing);
  if (this._readableState.flowing !== false) {
    debug('pause');
    this._readableState.flowing = false;
    this.emit('pause');
  }
  this._readableState.paused = true;
  return this;
};
function flow(stream) {
  var state = stream._readableState;
  debug('flow', state.flowing);
  while (state.flowing && stream.read() !== null);
}

// wrap an old-style stream as the async data source.
// This is *not* part of the readable stream interface.
// It is an ugly unfortunate mess of history.
Readable.prototype.wrap = function (stream) {
  var _this = this;
  var state = this._readableState;
  var paused = false;
  stream.on('end', function () {
    debug('wrapped end');
    if (state.decoder && !state.ended) {
      var chunk = state.decoder.end();
      if (chunk && chunk.length) _this.push(chunk);
    }
    _this.push(null);
  });
  stream.on('data', function (chunk) {
    debug('wrapped data');
    if (state.decoder) chunk = state.decoder.write(chunk);

    // don't skip over falsy values in objectMode
    if (state.objectMode && (chunk === null || chunk === undefined)) return;else if (!state.objectMode && (!chunk || !chunk.length)) return;
    var ret = _this.push(chunk);
    if (!ret) {
      paused = true;
      stream.pause();
    }
  });

  // proxy all the other methods.
  // important when wrapping filters and duplexes.
  for (var i in stream) {
    if (this[i] === undefined && typeof stream[i] === 'function') {
      this[i] = function methodWrap(method) {
        return function methodWrapReturnFunction() {
          return stream[method].apply(stream, arguments);
        };
      }(i);
    }
  }

  // proxy certain important events.
  for (var n = 0; n < kProxyEvents.length; n++) {
    stream.on(kProxyEvents[n], this.emit.bind(this, kProxyEvents[n]));
  }

  // when we try to consume some more bytes, simply unpause the
  // underlying stream.
  this._read = function (n) {
    debug('wrapped _read', n);
    if (paused) {
      paused = false;
      stream.resume();
    }
  };
  return this;
};
if (typeof Symbol === 'function') {
  Readable.prototype[Symbol.asyncIterator] = function () {
    if (createReadableStreamAsyncIterator === undefined) {
      createReadableStreamAsyncIterator = __webpack_require__(79676);
    }
    return createReadableStreamAsyncIterator(this);
  };
}
Object.defineProperty(Readable.prototype, 'readableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._readableState.highWaterMark;
  }
});
Object.defineProperty(Readable.prototype, 'readableBuffer', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._readableState && this._readableState.buffer;
  }
});
Object.defineProperty(Readable.prototype, 'readableFlowing', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._readableState.flowing;
  },
  set: function set(state) {
    if (this._readableState) {
      this._readableState.flowing = state;
    }
  }
});

// exposed for testing purposes only.
Readable._fromList = fromList;
Object.defineProperty(Readable.prototype, 'readableLength', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._readableState.length;
  }
});

// Pluck off n bytes from an array of buffers.
// Length is the combined lengths of all the buffers in the list.
// This function is designed to be inlinable, so please take care when making
// changes to the function body.
function fromList(n, state) {
  // nothing buffered
  if (state.length === 0) return null;
  var ret;
  if (state.objectMode) ret = state.buffer.shift();else if (!n || n >= state.length) {
    // read it all, truncate the list
    if (state.decoder) ret = state.buffer.join('');else if (state.buffer.length === 1) ret = state.buffer.first();else ret = state.buffer.concat(state.length);
    state.buffer.clear();
  } else {
    // read part of list
    ret = state.buffer.consume(n, state.decoder);
  }
  return ret;
}
function endReadable(stream) {
  var state = stream._readableState;
  debug('endReadable', state.endEmitted);
  if (!state.endEmitted) {
    state.ended = true;
    process.nextTick(endReadableNT, state, stream);
  }
}
function endReadableNT(state, stream) {
  debug('endReadableNT', state.endEmitted, state.length);

  // Check that we didn't get one last unshift.
  if (!state.endEmitted && state.length === 0) {
    state.endEmitted = true;
    stream.readable = false;
    stream.emit('end');
    if (state.autoDestroy) {
      // In case of duplex streams we need a way to detect
      // if the writable side is ready for autoDestroy as well
      var wState = stream._writableState;
      if (!wState || wState.autoDestroy && wState.finished) {
        stream.destroy();
      }
    }
  }
}
if (typeof Symbol === 'function') {
  Readable.from = function (iterable, opts) {
    if (from === undefined) {
      from = __webpack_require__(60378);
    }
    return from(Readable, iterable, opts);
  };
}
function indexOf(xs, x) {
  for (var i = 0, l = xs.length; i < l; i++) {
    if (xs[i] === x) return i;
  }
  return -1;
}

/***/ }),

/***/ 48569:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// a transform stream is a readable/writable stream where you do
// something with the data.  Sometimes it's called a "filter",
// but that's not a great name for it, since that implies a thing where
// some bits pass through, and others are simply ignored.  (That would
// be a valid example of a transform, of course.)
//
// While the output is causally related to the input, it's not a
// necessarily symmetric or synchronous transformation.  For example,
// a zlib stream might take multiple plain-text writes(), and then
// emit a single compressed chunk some time in the future.
//
// Here's how this works:
//
// The Transform stream has all the aspects of the readable and writable
// stream classes.  When you write(chunk), that calls _write(chunk,cb)
// internally, and returns false if there's a lot of pending writes
// buffered up.  When you call read(), that calls _read(n) until
// there's enough pending readable data buffered up.
//
// In a transform stream, the written data is placed in a buffer.  When
// _read(n) is called, it transforms the queued up data, calling the
// buffered _write cb's as it consumes chunks.  If consuming a single
// written chunk would result in multiple output chunks, then the first
// outputted bit calls the readcb, and subsequent chunks just go into
// the read buffer, and will cause it to emit 'readable' if necessary.
//
// This way, back-pressure is actually determined by the reading side,
// since _read has to be called to start processing a new chunk.  However,
// a pathological inflate type of transform can cause excessive buffering
// here.  For example, imagine a stream where every byte of input is
// interpreted as an integer from 0-255, and then results in that many
// bytes of output.  Writing the 4 bytes {ff,ff,ff,ff} would result in
// 1kb of data being output.  In this case, you could write a very small
// amount of input, and end up with a very large amount of output.  In
// such a pathological inflating mechanism, there'd be no way to tell
// the system to stop doing the transform.  A single 4MB write could
// cause the system to run out of memory.
//
// However, even in such a pathological case, only a single written chunk
// would be consumed, and then the rest would wait (un-transformed) until
// the results of the previous transformed chunk were consumed.



module.exports = Transform;
var _require$codes = (__webpack_require__(83797)/* .codes */ .F),
  ERR_METHOD_NOT_IMPLEMENTED = _require$codes.ERR_METHOD_NOT_IMPLEMENTED,
  ERR_MULTIPLE_CALLBACK = _require$codes.ERR_MULTIPLE_CALLBACK,
  ERR_TRANSFORM_ALREADY_TRANSFORMING = _require$codes.ERR_TRANSFORM_ALREADY_TRANSFORMING,
  ERR_TRANSFORM_WITH_LENGTH_0 = _require$codes.ERR_TRANSFORM_WITH_LENGTH_0;
var Duplex = __webpack_require__(14903);
__webpack_require__(89784)(Transform, Duplex);
function afterTransform(er, data) {
  var ts = this._transformState;
  ts.transforming = false;
  var cb = ts.writecb;
  if (cb === null) {
    return this.emit('error', new ERR_MULTIPLE_CALLBACK());
  }
  ts.writechunk = null;
  ts.writecb = null;
  if (data != null)
    // single equals check for both `null` and `undefined`
    this.push(data);
  cb(er);
  var rs = this._readableState;
  rs.reading = false;
  if (rs.needReadable || rs.length < rs.highWaterMark) {
    this._read(rs.highWaterMark);
  }
}
function Transform(options) {
  if (!(this instanceof Transform)) return new Transform(options);
  Duplex.call(this, options);
  this._transformState = {
    afterTransform: afterTransform.bind(this),
    needTransform: false,
    transforming: false,
    writecb: null,
    writechunk: null,
    writeencoding: null
  };

  // start out asking for a readable event once data is transformed.
  this._readableState.needReadable = true;

  // we have implemented the _read method, and done the other things
  // that Readable wants before the first _read call, so unset the
  // sync guard flag.
  this._readableState.sync = false;
  if (options) {
    if (typeof options.transform === 'function') this._transform = options.transform;
    if (typeof options.flush === 'function') this._flush = options.flush;
  }

  // When the writable side finishes, then flush out anything remaining.
  this.on('prefinish', prefinish);
}
function prefinish() {
  var _this = this;
  if (typeof this._flush === 'function' && !this._readableState.destroyed) {
    this._flush(function (er, data) {
      done(_this, er, data);
    });
  } else {
    done(this, null, null);
  }
}
Transform.prototype.push = function (chunk, encoding) {
  this._transformState.needTransform = false;
  return Duplex.prototype.push.call(this, chunk, encoding);
};

// This is the part where you do stuff!
// override this function in implementation classes.
// 'chunk' is an input chunk.
//
// Call `push(newChunk)` to pass along transformed output
// to the readable side.  You may call 'push' zero or more times.
//
// Call `cb(err)` when you are done with this chunk.  If you pass
// an error, then that'll put the hurt on the whole operation.  If you
// never call cb(), then you'll never get another chunk.
Transform.prototype._transform = function (chunk, encoding, cb) {
  cb(new ERR_METHOD_NOT_IMPLEMENTED('_transform()'));
};
Transform.prototype._write = function (chunk, encoding, cb) {
  var ts = this._transformState;
  ts.writecb = cb;
  ts.writechunk = chunk;
  ts.writeencoding = encoding;
  if (!ts.transforming) {
    var rs = this._readableState;
    if (ts.needTransform || rs.needReadable || rs.length < rs.highWaterMark) this._read(rs.highWaterMark);
  }
};

// Doesn't matter what the args are here.
// _transform does all the work.
// That we got here means that the readable side wants more data.
Transform.prototype._read = function (n) {
  var ts = this._transformState;
  if (ts.writechunk !== null && !ts.transforming) {
    ts.transforming = true;
    this._transform(ts.writechunk, ts.writeencoding, ts.afterTransform);
  } else {
    // mark that we need a transform, so that any data that comes in
    // will get processed, now that we've asked for it.
    ts.needTransform = true;
  }
};
Transform.prototype._destroy = function (err, cb) {
  Duplex.prototype._destroy.call(this, err, function (err2) {
    cb(err2);
  });
};
function done(stream, er, data) {
  if (er) return stream.emit('error', er);
  if (data != null)
    // single equals check for both `null` and `undefined`
    stream.push(data);

  // TODO(BridgeAR): Write a test for these two error cases
  // if there's nothing in the write buffer, then that means
  // that nothing more will ever be provided
  if (stream._writableState.length) throw new ERR_TRANSFORM_WITH_LENGTH_0();
  if (stream._transformState.transforming) throw new ERR_TRANSFORM_ALREADY_TRANSFORMING();
  return stream.push(null);
}

/***/ }),

/***/ 29781:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

// A bit simpler than readable streams.
// Implement an async ._write(chunk, encoding, cb), and it'll handle all
// the drain event emission and buffering.



module.exports = Writable;

/* <replacement> */
function WriteReq(chunk, encoding, cb) {
  this.chunk = chunk;
  this.encoding = encoding;
  this.callback = cb;
  this.next = null;
}

// It seems a linked list but it is not
// there will be only 2 of these for each stream
function CorkedRequest(state) {
  var _this = this;
  this.next = null;
  this.entry = null;
  this.finish = function () {
    onCorkedFinish(_this, state);
  };
}
/* </replacement> */

/*<replacement>*/
var Duplex;
/*</replacement>*/

Writable.WritableState = WritableState;

/*<replacement>*/
var internalUtil = {
  deprecate: __webpack_require__(16465)
};
/*</replacement>*/

/*<replacement>*/
var Stream = __webpack_require__(99018);
/*</replacement>*/

var Buffer = (__webpack_require__(14598).Buffer);
var OurUint8Array = (typeof __webpack_require__.g !== 'undefined' ? __webpack_require__.g : typeof window !== 'undefined' ? window : typeof self !== 'undefined' ? self : {}).Uint8Array || function () {};
function _uint8ArrayToBuffer(chunk) {
  return Buffer.from(chunk);
}
function _isUint8Array(obj) {
  return Buffer.isBuffer(obj) || obj instanceof OurUint8Array;
}
var destroyImpl = __webpack_require__(37385);
var _require = __webpack_require__(68130),
  getHighWaterMark = _require.getHighWaterMark;
var _require$codes = (__webpack_require__(83797)/* .codes */ .F),
  ERR_INVALID_ARG_TYPE = _require$codes.ERR_INVALID_ARG_TYPE,
  ERR_METHOD_NOT_IMPLEMENTED = _require$codes.ERR_METHOD_NOT_IMPLEMENTED,
  ERR_MULTIPLE_CALLBACK = _require$codes.ERR_MULTIPLE_CALLBACK,
  ERR_STREAM_CANNOT_PIPE = _require$codes.ERR_STREAM_CANNOT_PIPE,
  ERR_STREAM_DESTROYED = _require$codes.ERR_STREAM_DESTROYED,
  ERR_STREAM_NULL_VALUES = _require$codes.ERR_STREAM_NULL_VALUES,
  ERR_STREAM_WRITE_AFTER_END = _require$codes.ERR_STREAM_WRITE_AFTER_END,
  ERR_UNKNOWN_ENCODING = _require$codes.ERR_UNKNOWN_ENCODING;
var errorOrDestroy = destroyImpl.errorOrDestroy;
__webpack_require__(89784)(Writable, Stream);
function nop() {}
function WritableState(options, stream, isDuplex) {
  Duplex = Duplex || __webpack_require__(14903);
  options = options || {};

  // Duplex streams are both readable and writable, but share
  // the same options object.
  // However, some cases require setting options to different
  // values for the readable and the writable sides of the duplex stream,
  // e.g. options.readableObjectMode vs. options.writableObjectMode, etc.
  if (typeof isDuplex !== 'boolean') isDuplex = stream instanceof Duplex;

  // object stream flag to indicate whether or not this stream
  // contains buffers or objects.
  this.objectMode = !!options.objectMode;
  if (isDuplex) this.objectMode = this.objectMode || !!options.writableObjectMode;

  // the point at which write() starts returning false
  // Note: 0 is a valid value, means that we always return false if
  // the entire buffer is not flushed immediately on write()
  this.highWaterMark = getHighWaterMark(this, options, 'writableHighWaterMark', isDuplex);

  // if _final has been called
  this.finalCalled = false;

  // drain event flag.
  this.needDrain = false;
  // at the start of calling end()
  this.ending = false;
  // when end() has been called, and returned
  this.ended = false;
  // when 'finish' is emitted
  this.finished = false;

  // has it been destroyed
  this.destroyed = false;

  // should we decode strings into buffers before passing to _write?
  // this is here so that some node-core streams can optimize string
  // handling at a lower level.
  var noDecode = options.decodeStrings === false;
  this.decodeStrings = !noDecode;

  // Crypto is kind of old and crusty.  Historically, its default string
  // encoding is 'binary' so we have to make this configurable.
  // Everything else in the universe uses 'utf8', though.
  this.defaultEncoding = options.defaultEncoding || 'utf8';

  // not an actual buffer we keep track of, but a measurement
  // of how much we're waiting to get pushed to some underlying
  // socket or file.
  this.length = 0;

  // a flag to see when we're in the middle of a write.
  this.writing = false;

  // when true all writes will be buffered until .uncork() call
  this.corked = 0;

  // a flag to be able to tell if the onwrite cb is called immediately,
  // or on a later tick.  We set this to true at first, because any
  // actions that shouldn't happen until "later" should generally also
  // not happen before the first write call.
  this.sync = true;

  // a flag to know if we're processing previously buffered items, which
  // may call the _write() callback in the same tick, so that we don't
  // end up in an overlapped onwrite situation.
  this.bufferProcessing = false;

  // the callback that's passed to _write(chunk,cb)
  this.onwrite = function (er) {
    onwrite(stream, er);
  };

  // the callback that the user supplies to write(chunk,encoding,cb)
  this.writecb = null;

  // the amount that is being written when _write is called.
  this.writelen = 0;
  this.bufferedRequest = null;
  this.lastBufferedRequest = null;

  // number of pending user-supplied write callbacks
  // this must be 0 before 'finish' can be emitted
  this.pendingcb = 0;

  // emit prefinish if the only thing we're waiting for is _write cbs
  // This is relevant for synchronous Transform streams
  this.prefinished = false;

  // True if the error was already emitted and should not be thrown again
  this.errorEmitted = false;

  // Should close be emitted on destroy. Defaults to true.
  this.emitClose = options.emitClose !== false;

  // Should .destroy() be called after 'finish' (and potentially 'end')
  this.autoDestroy = !!options.autoDestroy;

  // count buffered requests
  this.bufferedRequestCount = 0;

  // allocate the first CorkedRequest, there is always
  // one allocated and free to use, and we maintain at most two
  this.corkedRequestsFree = new CorkedRequest(this);
}
WritableState.prototype.getBuffer = function getBuffer() {
  var current = this.bufferedRequest;
  var out = [];
  while (current) {
    out.push(current);
    current = current.next;
  }
  return out;
};
(function () {
  try {
    Object.defineProperty(WritableState.prototype, 'buffer', {
      get: internalUtil.deprecate(function writableStateBufferGetter() {
        return this.getBuffer();
      }, '_writableState.buffer is deprecated. Use _writableState.getBuffer ' + 'instead.', 'DEP0003')
    });
  } catch (_) {}
})();

// Test _writableState for inheritance to account for Duplex streams,
// whose prototype chain only points to Readable.
var realHasInstance;
if (typeof Symbol === 'function' && Symbol.hasInstance && typeof Function.prototype[Symbol.hasInstance] === 'function') {
  realHasInstance = Function.prototype[Symbol.hasInstance];
  Object.defineProperty(Writable, Symbol.hasInstance, {
    value: function value(object) {
      if (realHasInstance.call(this, object)) return true;
      if (this !== Writable) return false;
      return object && object._writableState instanceof WritableState;
    }
  });
} else {
  realHasInstance = function realHasInstance(object) {
    return object instanceof this;
  };
}
function Writable(options) {
  Duplex = Duplex || __webpack_require__(14903);

  // Writable ctor is applied to Duplexes, too.
  // `realHasInstance` is necessary because using plain `instanceof`
  // would return false, as no `_writableState` property is attached.

  // Trying to use the custom `instanceof` for Writable here will also break the
  // Node.js LazyTransform implementation, which has a non-trivial getter for
  // `_writableState` that would lead to infinite recursion.

  // Checking for a Stream.Duplex instance is faster here instead of inside
  // the WritableState constructor, at least with V8 6.5
  var isDuplex = this instanceof Duplex;
  if (!isDuplex && !realHasInstance.call(Writable, this)) return new Writable(options);
  this._writableState = new WritableState(options, this, isDuplex);

  // legacy.
  this.writable = true;
  if (options) {
    if (typeof options.write === 'function') this._write = options.write;
    if (typeof options.writev === 'function') this._writev = options.writev;
    if (typeof options.destroy === 'function') this._destroy = options.destroy;
    if (typeof options.final === 'function') this._final = options.final;
  }
  Stream.call(this);
}

// Otherwise people can pipe Writable streams, which is just wrong.
Writable.prototype.pipe = function () {
  errorOrDestroy(this, new ERR_STREAM_CANNOT_PIPE());
};
function writeAfterEnd(stream, cb) {
  var er = new ERR_STREAM_WRITE_AFTER_END();
  // TODO: defer error events consistently everywhere, not just the cb
  errorOrDestroy(stream, er);
  process.nextTick(cb, er);
}

// Checks that a user-supplied chunk is valid, especially for the particular
// mode the stream is in. Currently this means that `null` is never accepted
// and undefined/non-string values are only allowed in object mode.
function validChunk(stream, state, chunk, cb) {
  var er;
  if (chunk === null) {
    er = new ERR_STREAM_NULL_VALUES();
  } else if (typeof chunk !== 'string' && !state.objectMode) {
    er = new ERR_INVALID_ARG_TYPE('chunk', ['string', 'Buffer'], chunk);
  }
  if (er) {
    errorOrDestroy(stream, er);
    process.nextTick(cb, er);
    return false;
  }
  return true;
}
Writable.prototype.write = function (chunk, encoding, cb) {
  var state = this._writableState;
  var ret = false;
  var isBuf = !state.objectMode && _isUint8Array(chunk);
  if (isBuf && !Buffer.isBuffer(chunk)) {
    chunk = _uint8ArrayToBuffer(chunk);
  }
  if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }
  if (isBuf) encoding = 'buffer';else if (!encoding) encoding = state.defaultEncoding;
  if (typeof cb !== 'function') cb = nop;
  if (state.ending) writeAfterEnd(this, cb);else if (isBuf || validChunk(this, state, chunk, cb)) {
    state.pendingcb++;
    ret = writeOrBuffer(this, state, isBuf, chunk, encoding, cb);
  }
  return ret;
};
Writable.prototype.cork = function () {
  this._writableState.corked++;
};
Writable.prototype.uncork = function () {
  var state = this._writableState;
  if (state.corked) {
    state.corked--;
    if (!state.writing && !state.corked && !state.bufferProcessing && state.bufferedRequest) clearBuffer(this, state);
  }
};
Writable.prototype.setDefaultEncoding = function setDefaultEncoding(encoding) {
  // node::ParseEncoding() requires lower case.
  if (typeof encoding === 'string') encoding = encoding.toLowerCase();
  if (!(['hex', 'utf8', 'utf-8', 'ascii', 'binary', 'base64', 'ucs2', 'ucs-2', 'utf16le', 'utf-16le', 'raw'].indexOf((encoding + '').toLowerCase()) > -1)) throw new ERR_UNKNOWN_ENCODING(encoding);
  this._writableState.defaultEncoding = encoding;
  return this;
};
Object.defineProperty(Writable.prototype, 'writableBuffer', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState && this._writableState.getBuffer();
  }
});
function decodeChunk(state, chunk, encoding) {
  if (!state.objectMode && state.decodeStrings !== false && typeof chunk === 'string') {
    chunk = Buffer.from(chunk, encoding);
  }
  return chunk;
}
Object.defineProperty(Writable.prototype, 'writableHighWaterMark', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState.highWaterMark;
  }
});

// if we're already writing something, then just put this
// in the queue, and wait our turn.  Otherwise, call _write
// If we return false, then we need a drain event, so set that flag.
function writeOrBuffer(stream, state, isBuf, chunk, encoding, cb) {
  if (!isBuf) {
    var newChunk = decodeChunk(state, chunk, encoding);
    if (chunk !== newChunk) {
      isBuf = true;
      encoding = 'buffer';
      chunk = newChunk;
    }
  }
  var len = state.objectMode ? 1 : chunk.length;
  state.length += len;
  var ret = state.length < state.highWaterMark;
  // we must ensure that previous needDrain will not be reset to false.
  if (!ret) state.needDrain = true;
  if (state.writing || state.corked) {
    var last = state.lastBufferedRequest;
    state.lastBufferedRequest = {
      chunk: chunk,
      encoding: encoding,
      isBuf: isBuf,
      callback: cb,
      next: null
    };
    if (last) {
      last.next = state.lastBufferedRequest;
    } else {
      state.bufferedRequest = state.lastBufferedRequest;
    }
    state.bufferedRequestCount += 1;
  } else {
    doWrite(stream, state, false, len, chunk, encoding, cb);
  }
  return ret;
}
function doWrite(stream, state, writev, len, chunk, encoding, cb) {
  state.writelen = len;
  state.writecb = cb;
  state.writing = true;
  state.sync = true;
  if (state.destroyed) state.onwrite(new ERR_STREAM_DESTROYED('write'));else if (writev) stream._writev(chunk, state.onwrite);else stream._write(chunk, encoding, state.onwrite);
  state.sync = false;
}
function onwriteError(stream, state, sync, er, cb) {
  --state.pendingcb;
  if (sync) {
    // defer the callback if we are being called synchronously
    // to avoid piling up things on the stack
    process.nextTick(cb, er);
    // this can emit finish, and it will always happen
    // after error
    process.nextTick(finishMaybe, stream, state);
    stream._writableState.errorEmitted = true;
    errorOrDestroy(stream, er);
  } else {
    // the caller expect this to happen before if
    // it is async
    cb(er);
    stream._writableState.errorEmitted = true;
    errorOrDestroy(stream, er);
    // this can emit finish, but finish must
    // always follow error
    finishMaybe(stream, state);
  }
}
function onwriteStateUpdate(state) {
  state.writing = false;
  state.writecb = null;
  state.length -= state.writelen;
  state.writelen = 0;
}
function onwrite(stream, er) {
  var state = stream._writableState;
  var sync = state.sync;
  var cb = state.writecb;
  if (typeof cb !== 'function') throw new ERR_MULTIPLE_CALLBACK();
  onwriteStateUpdate(state);
  if (er) onwriteError(stream, state, sync, er, cb);else {
    // Check if we're actually ready to finish, but don't emit yet
    var finished = needFinish(state) || stream.destroyed;
    if (!finished && !state.corked && !state.bufferProcessing && state.bufferedRequest) {
      clearBuffer(stream, state);
    }
    if (sync) {
      process.nextTick(afterWrite, stream, state, finished, cb);
    } else {
      afterWrite(stream, state, finished, cb);
    }
  }
}
function afterWrite(stream, state, finished, cb) {
  if (!finished) onwriteDrain(stream, state);
  state.pendingcb--;
  cb();
  finishMaybe(stream, state);
}

// Must force callback to be called on nextTick, so that we don't
// emit 'drain' before the write() consumer gets the 'false' return
// value, and has a chance to attach a 'drain' listener.
function onwriteDrain(stream, state) {
  if (state.length === 0 && state.needDrain) {
    state.needDrain = false;
    stream.emit('drain');
  }
}

// if there's something in the buffer waiting, then process it
function clearBuffer(stream, state) {
  state.bufferProcessing = true;
  var entry = state.bufferedRequest;
  if (stream._writev && entry && entry.next) {
    // Fast case, write everything using _writev()
    var l = state.bufferedRequestCount;
    var buffer = new Array(l);
    var holder = state.corkedRequestsFree;
    holder.entry = entry;
    var count = 0;
    var allBuffers = true;
    while (entry) {
      buffer[count] = entry;
      if (!entry.isBuf) allBuffers = false;
      entry = entry.next;
      count += 1;
    }
    buffer.allBuffers = allBuffers;
    doWrite(stream, state, true, state.length, buffer, '', holder.finish);

    // doWrite is almost always async, defer these to save a bit of time
    // as the hot path ends with doWrite
    state.pendingcb++;
    state.lastBufferedRequest = null;
    if (holder.next) {
      state.corkedRequestsFree = holder.next;
      holder.next = null;
    } else {
      state.corkedRequestsFree = new CorkedRequest(state);
    }
    state.bufferedRequestCount = 0;
  } else {
    // Slow case, write chunks one-by-one
    while (entry) {
      var chunk = entry.chunk;
      var encoding = entry.encoding;
      var cb = entry.callback;
      var len = state.objectMode ? 1 : chunk.length;
      doWrite(stream, state, false, len, chunk, encoding, cb);
      entry = entry.next;
      state.bufferedRequestCount--;
      // if we didn't call the onwrite immediately, then
      // it means that we need to wait until it does.
      // also, that means that the chunk and cb are currently
      // being processed, so move the buffer counter past them.
      if (state.writing) {
        break;
      }
    }
    if (entry === null) state.lastBufferedRequest = null;
  }
  state.bufferedRequest = entry;
  state.bufferProcessing = false;
}
Writable.prototype._write = function (chunk, encoding, cb) {
  cb(new ERR_METHOD_NOT_IMPLEMENTED('_write()'));
};
Writable.prototype._writev = null;
Writable.prototype.end = function (chunk, encoding, cb) {
  var state = this._writableState;
  if (typeof chunk === 'function') {
    cb = chunk;
    chunk = null;
    encoding = null;
  } else if (typeof encoding === 'function') {
    cb = encoding;
    encoding = null;
  }
  if (chunk !== null && chunk !== undefined) this.write(chunk, encoding);

  // .end() fully uncorks
  if (state.corked) {
    state.corked = 1;
    this.uncork();
  }

  // ignore unnecessary end() calls.
  if (!state.ending) endWritable(this, state, cb);
  return this;
};
Object.defineProperty(Writable.prototype, 'writableLength', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    return this._writableState.length;
  }
});
function needFinish(state) {
  return state.ending && state.length === 0 && state.bufferedRequest === null && !state.finished && !state.writing;
}
function callFinal(stream, state) {
  stream._final(function (err) {
    state.pendingcb--;
    if (err) {
      errorOrDestroy(stream, err);
    }
    state.prefinished = true;
    stream.emit('prefinish');
    finishMaybe(stream, state);
  });
}
function prefinish(stream, state) {
  if (!state.prefinished && !state.finalCalled) {
    if (typeof stream._final === 'function' && !state.destroyed) {
      state.pendingcb++;
      state.finalCalled = true;
      process.nextTick(callFinal, stream, state);
    } else {
      state.prefinished = true;
      stream.emit('prefinish');
    }
  }
}
function finishMaybe(stream, state) {
  var need = needFinish(state);
  if (need) {
    prefinish(stream, state);
    if (state.pendingcb === 0) {
      state.finished = true;
      stream.emit('finish');
      if (state.autoDestroy) {
        // In case of duplex streams we need a way to detect
        // if the readable side is ready for autoDestroy as well
        var rState = stream._readableState;
        if (!rState || rState.autoDestroy && rState.endEmitted) {
          stream.destroy();
        }
      }
    }
  }
  return need;
}
function endWritable(stream, state, cb) {
  state.ending = true;
  finishMaybe(stream, state);
  if (cb) {
    if (state.finished) process.nextTick(cb);else stream.once('finish', cb);
  }
  state.ended = true;
  stream.writable = false;
}
function onCorkedFinish(corkReq, state, err) {
  var entry = corkReq.entry;
  corkReq.entry = null;
  while (entry) {
    var cb = entry.callback;
    state.pendingcb--;
    cb(err);
    entry = entry.next;
  }

  // reuse the free corkReq.
  state.corkedRequestsFree.next = corkReq;
}
Object.defineProperty(Writable.prototype, 'destroyed', {
  // making it explicit this property is not enumerable
  // because otherwise some prototype manipulation in
  // userland will fail
  enumerable: false,
  get: function get() {
    if (this._writableState === undefined) {
      return false;
    }
    return this._writableState.destroyed;
  },
  set: function set(value) {
    // we ignore the value if the stream
    // has not been initialized yet
    if (!this._writableState) {
      return;
    }

    // backward compatibility, the user is explicitly
    // managing destroyed
    this._writableState.destroyed = value;
  }
});
Writable.prototype.destroy = destroyImpl.destroy;
Writable.prototype._undestroy = destroyImpl.undestroy;
Writable.prototype._destroy = function (err, cb) {
  cb(err);
};

/***/ }),

/***/ 79676:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);


var _Object$setPrototypeO;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var finished = __webpack_require__(12167);
var kLastResolve = Symbol('lastResolve');
var kLastReject = Symbol('lastReject');
var kError = Symbol('error');
var kEnded = Symbol('ended');
var kLastPromise = Symbol('lastPromise');
var kHandlePromise = Symbol('handlePromise');
var kStream = Symbol('stream');
function createIterResult(value, done) {
  return {
    value: value,
    done: done
  };
}
function readAndResolve(iter) {
  var resolve = iter[kLastResolve];
  if (resolve !== null) {
    var data = iter[kStream].read();
    // we defer if data is null
    // we can be expecting either 'end' or
    // 'error'
    if (data !== null) {
      iter[kLastPromise] = null;
      iter[kLastResolve] = null;
      iter[kLastReject] = null;
      resolve(createIterResult(data, false));
    }
  }
}
function onReadable(iter) {
  // we wait for the next tick, because it might
  // emit an error with process.nextTick
  process.nextTick(readAndResolve, iter);
}
function wrapForNext(lastPromise, iter) {
  return function (resolve, reject) {
    lastPromise.then(function () {
      if (iter[kEnded]) {
        resolve(createIterResult(undefined, true));
        return;
      }
      iter[kHandlePromise](resolve, reject);
    }, reject);
  };
}
var AsyncIteratorPrototype = Object.getPrototypeOf(function () {});
var ReadableStreamAsyncIteratorPrototype = Object.setPrototypeOf((_Object$setPrototypeO = {
  get stream() {
    return this[kStream];
  },
  next: function next() {
    var _this = this;
    // if we have detected an error in the meanwhile
    // reject straight away
    var error = this[kError];
    if (error !== null) {
      return Promise.reject(error);
    }
    if (this[kEnded]) {
      return Promise.resolve(createIterResult(undefined, true));
    }
    if (this[kStream].destroyed) {
      // We need to defer via nextTick because if .destroy(err) is
      // called, the error will be emitted via nextTick, and
      // we cannot guarantee that there is no error lingering around
      // waiting to be emitted.
      return new Promise(function (resolve, reject) {
        process.nextTick(function () {
          if (_this[kError]) {
            reject(_this[kError]);
          } else {
            resolve(createIterResult(undefined, true));
          }
        });
      });
    }

    // if we have multiple next() calls
    // we will wait for the previous Promise to finish
    // this logic is optimized to support for await loops,
    // where next() is only called once at a time
    var lastPromise = this[kLastPromise];
    var promise;
    if (lastPromise) {
      promise = new Promise(wrapForNext(lastPromise, this));
    } else {
      // fast path needed to support multiple this.push()
      // without triggering the next() queue
      var data = this[kStream].read();
      if (data !== null) {
        return Promise.resolve(createIterResult(data, false));
      }
      promise = new Promise(this[kHandlePromise]);
    }
    this[kLastPromise] = promise;
    return promise;
  }
}, _defineProperty(_Object$setPrototypeO, Symbol.asyncIterator, function () {
  return this;
}), _defineProperty(_Object$setPrototypeO, "return", function _return() {
  var _this2 = this;
  // destroy(err, cb) is a private API
  // we can guarantee we have that here, because we control the
  // Readable class this is attached to
  return new Promise(function (resolve, reject) {
    _this2[kStream].destroy(null, function (err) {
      if (err) {
        reject(err);
        return;
      }
      resolve(createIterResult(undefined, true));
    });
  });
}), _Object$setPrototypeO), AsyncIteratorPrototype);
var createReadableStreamAsyncIterator = function createReadableStreamAsyncIterator(stream) {
  var _Object$create;
  var iterator = Object.create(ReadableStreamAsyncIteratorPrototype, (_Object$create = {}, _defineProperty(_Object$create, kStream, {
    value: stream,
    writable: true
  }), _defineProperty(_Object$create, kLastResolve, {
    value: null,
    writable: true
  }), _defineProperty(_Object$create, kLastReject, {
    value: null,
    writable: true
  }), _defineProperty(_Object$create, kError, {
    value: null,
    writable: true
  }), _defineProperty(_Object$create, kEnded, {
    value: stream._readableState.endEmitted,
    writable: true
  }), _defineProperty(_Object$create, kHandlePromise, {
    value: function value(resolve, reject) {
      var data = iterator[kStream].read();
      if (data) {
        iterator[kLastPromise] = null;
        iterator[kLastResolve] = null;
        iterator[kLastReject] = null;
        resolve(createIterResult(data, false));
      } else {
        iterator[kLastResolve] = resolve;
        iterator[kLastReject] = reject;
      }
    },
    writable: true
  }), _Object$create));
  iterator[kLastPromise] = null;
  finished(stream, function (err) {
    if (err && err.code !== 'ERR_STREAM_PREMATURE_CLOSE') {
      var reject = iterator[kLastReject];
      // reject if we are waiting for data in the Promise
      // returned by next() and store the error
      if (reject !== null) {
        iterator[kLastPromise] = null;
        iterator[kLastResolve] = null;
        iterator[kLastReject] = null;
        reject(err);
      }
      iterator[kError] = err;
      return;
    }
    var resolve = iterator[kLastResolve];
    if (resolve !== null) {
      iterator[kLastPromise] = null;
      iterator[kLastResolve] = null;
      iterator[kLastReject] = null;
      resolve(createIterResult(undefined, true));
    }
    iterator[kEnded] = true;
  });
  stream.on('readable', onReadable.bind(null, iterator));
  return iterator;
};
module.exports = createReadableStreamAsyncIterator;

/***/ }),

/***/ 37385:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var process = __webpack_require__(9964);


// undocumented cb() API, needed for core, not for public API
function destroy(err, cb) {
  var _this = this;
  var readableDestroyed = this._readableState && this._readableState.destroyed;
  var writableDestroyed = this._writableState && this._writableState.destroyed;
  if (readableDestroyed || writableDestroyed) {
    if (cb) {
      cb(err);
    } else if (err) {
      if (!this._writableState) {
        process.nextTick(emitErrorNT, this, err);
      } else if (!this._writableState.errorEmitted) {
        this._writableState.errorEmitted = true;
        process.nextTick(emitErrorNT, this, err);
      }
    }
    return this;
  }

  // we set destroyed to true before firing error callbacks in order
  // to make it re-entrance safe in case destroy() is called within callbacks

  if (this._readableState) {
    this._readableState.destroyed = true;
  }

  // if this is a duplex stream mark the writable part as destroyed as well
  if (this._writableState) {
    this._writableState.destroyed = true;
  }
  this._destroy(err || null, function (err) {
    if (!cb && err) {
      if (!_this._writableState) {
        process.nextTick(emitErrorAndCloseNT, _this, err);
      } else if (!_this._writableState.errorEmitted) {
        _this._writableState.errorEmitted = true;
        process.nextTick(emitErrorAndCloseNT, _this, err);
      } else {
        process.nextTick(emitCloseNT, _this);
      }
    } else if (cb) {
      process.nextTick(emitCloseNT, _this);
      cb(err);
    } else {
      process.nextTick(emitCloseNT, _this);
    }
  });
  return this;
}
function emitErrorAndCloseNT(self, err) {
  emitErrorNT(self, err);
  emitCloseNT(self);
}
function emitCloseNT(self) {
  if (self._writableState && !self._writableState.emitClose) return;
  if (self._readableState && !self._readableState.emitClose) return;
  self.emit('close');
}
function undestroy() {
  if (this._readableState) {
    this._readableState.destroyed = false;
    this._readableState.reading = false;
    this._readableState.ended = false;
    this._readableState.endEmitted = false;
  }
  if (this._writableState) {
    this._writableState.destroyed = false;
    this._writableState.ended = false;
    this._writableState.ending = false;
    this._writableState.finalCalled = false;
    this._writableState.prefinished = false;
    this._writableState.finished = false;
    this._writableState.errorEmitted = false;
  }
}
function emitErrorNT(self, err) {
  self.emit('error', err);
}
function errorOrDestroy(stream, err) {
  // We have tests that rely on errors being emitted
  // in the same tick, so changing this is semver major.
  // For now when you opt-in to autoDestroy we allow
  // the error to be emitted nextTick. In a future
  // semver major update we should change the default to this.

  var rState = stream._readableState;
  var wState = stream._writableState;
  if (rState && rState.autoDestroy || wState && wState.autoDestroy) stream.destroy(err);else stream.emit('error', err);
}
module.exports = {
  destroy: destroy,
  undestroy: undestroy,
  errorOrDestroy: errorOrDestroy
};

/***/ }),

/***/ 12167:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Ported from https://github.com/mafintosh/end-of-stream with
// permission from the author, Mathias Buus (@mafintosh).



var ERR_STREAM_PREMATURE_CLOSE = (__webpack_require__(83797)/* .codes */ .F).ERR_STREAM_PREMATURE_CLOSE;
function once(callback) {
  var called = false;
  return function () {
    if (called) return;
    called = true;
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    callback.apply(this, args);
  };
}
function noop() {}
function isRequest(stream) {
  return stream.setHeader && typeof stream.abort === 'function';
}
function eos(stream, opts, callback) {
  if (typeof opts === 'function') return eos(stream, null, opts);
  if (!opts) opts = {};
  callback = once(callback || noop);
  var readable = opts.readable || opts.readable !== false && stream.readable;
  var writable = opts.writable || opts.writable !== false && stream.writable;
  var onlegacyfinish = function onlegacyfinish() {
    if (!stream.writable) onfinish();
  };
  var writableEnded = stream._writableState && stream._writableState.finished;
  var onfinish = function onfinish() {
    writable = false;
    writableEnded = true;
    if (!readable) callback.call(stream);
  };
  var readableEnded = stream._readableState && stream._readableState.endEmitted;
  var onend = function onend() {
    readable = false;
    readableEnded = true;
    if (!writable) callback.call(stream);
  };
  var onerror = function onerror(err) {
    callback.call(stream, err);
  };
  var onclose = function onclose() {
    var err;
    if (readable && !readableEnded) {
      if (!stream._readableState || !stream._readableState.ended) err = new ERR_STREAM_PREMATURE_CLOSE();
      return callback.call(stream, err);
    }
    if (writable && !writableEnded) {
      if (!stream._writableState || !stream._writableState.ended) err = new ERR_STREAM_PREMATURE_CLOSE();
      return callback.call(stream, err);
    }
  };
  var onrequest = function onrequest() {
    stream.req.on('finish', onfinish);
  };
  if (isRequest(stream)) {
    stream.on('complete', onfinish);
    stream.on('abort', onclose);
    if (stream.req) onrequest();else stream.on('request', onrequest);
  } else if (writable && !stream._writableState) {
    // legacy streams
    stream.on('end', onlegacyfinish);
    stream.on('close', onlegacyfinish);
  }
  stream.on('end', onend);
  stream.on('finish', onfinish);
  if (opts.error !== false) stream.on('error', onerror);
  stream.on('close', onclose);
  return function () {
    stream.removeListener('complete', onfinish);
    stream.removeListener('abort', onclose);
    stream.removeListener('request', onrequest);
    if (stream.req) stream.req.removeListener('finish', onfinish);
    stream.removeListener('end', onlegacyfinish);
    stream.removeListener('close', onlegacyfinish);
    stream.removeListener('finish', onfinish);
    stream.removeListener('end', onend);
    stream.removeListener('error', onerror);
    stream.removeListener('close', onclose);
  };
}
module.exports = eos;

/***/ }),

/***/ 60378:
/***/ (function(module) {

module.exports = function () {
  throw new Error('Readable.from is not available in the browser')
};


/***/ }),

/***/ 43765:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
// Ported from https://github.com/mafintosh/pump with
// permission from the author, Mathias Buus (@mafintosh).



var eos;
function once(callback) {
  var called = false;
  return function () {
    if (called) return;
    called = true;
    callback.apply(void 0, arguments);
  };
}
var _require$codes = (__webpack_require__(83797)/* .codes */ .F),
  ERR_MISSING_ARGS = _require$codes.ERR_MISSING_ARGS,
  ERR_STREAM_DESTROYED = _require$codes.ERR_STREAM_DESTROYED;
function noop(err) {
  // Rethrow the error if it exists to avoid swallowing it
  if (err) throw err;
}
function isRequest(stream) {
  return stream.setHeader && typeof stream.abort === 'function';
}
function destroyer(stream, reading, writing, callback) {
  callback = once(callback);
  var closed = false;
  stream.on('close', function () {
    closed = true;
  });
  if (eos === undefined) eos = __webpack_require__(12167);
  eos(stream, {
    readable: reading,
    writable: writing
  }, function (err) {
    if (err) return callback(err);
    closed = true;
    callback();
  });
  var destroyed = false;
  return function (err) {
    if (closed) return;
    if (destroyed) return;
    destroyed = true;

    // request.destroy just do .end - .abort is what we want
    if (isRequest(stream)) return stream.abort();
    if (typeof stream.destroy === 'function') return stream.destroy();
    callback(err || new ERR_STREAM_DESTROYED('pipe'));
  };
}
function call(fn) {
  fn();
}
function pipe(from, to) {
  return from.pipe(to);
}
function popCallback(streams) {
  if (!streams.length) return noop;
  if (typeof streams[streams.length - 1] !== 'function') return noop;
  return streams.pop();
}
function pipeline() {
  for (var _len = arguments.length, streams = new Array(_len), _key = 0; _key < _len; _key++) {
    streams[_key] = arguments[_key];
  }
  var callback = popCallback(streams);
  if (Array.isArray(streams[0])) streams = streams[0];
  if (streams.length < 2) {
    throw new ERR_MISSING_ARGS('streams');
  }
  var error;
  var destroys = streams.map(function (stream, i) {
    var reading = i < streams.length - 1;
    var writing = i > 0;
    return destroyer(stream, reading, writing, function (err) {
      if (!error) error = err;
      if (err) destroys.forEach(call);
      if (reading) return;
      destroys.forEach(call);
      callback(error);
    });
  });
  return streams.reduce(pipe);
}
module.exports = pipeline;

/***/ }),

/***/ 68130:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var ERR_INVALID_OPT_VALUE = (__webpack_require__(83797)/* .codes */ .F).ERR_INVALID_OPT_VALUE;
function highWaterMarkFrom(options, isDuplex, duplexKey) {
  return options.highWaterMark != null ? options.highWaterMark : isDuplex ? options[duplexKey] : null;
}
function getHighWaterMark(state, options, duplexKey, isDuplex) {
  var hwm = highWaterMarkFrom(options, isDuplex, duplexKey);
  if (hwm != null) {
    if (!(isFinite(hwm) && Math.floor(hwm) === hwm) || hwm < 0) {
      var name = isDuplex ? duplexKey : 'highWaterMark';
      throw new ERR_INVALID_OPT_VALUE(name, hwm);
    }
    return Math.floor(hwm);
  }

  // Default value
  return state.objectMode ? 16 : 16 * 1024;
}
module.exports = {
  getHighWaterMark: getHighWaterMark
};

/***/ }),

/***/ 99018:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

module.exports = __webpack_require__(64785).EventEmitter;


/***/ }),

/***/ 43143:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.



/*<replacement>*/

var Buffer = (__webpack_require__(11432).Buffer);
/*</replacement>*/

var isEncoding = Buffer.isEncoding || function (encoding) {
  encoding = '' + encoding;
  switch (encoding && encoding.toLowerCase()) {
    case 'hex':case 'utf8':case 'utf-8':case 'ascii':case 'binary':case 'base64':case 'ucs2':case 'ucs-2':case 'utf16le':case 'utf-16le':case 'raw':
      return true;
    default:
      return false;
  }
};

function _normalizeEncoding(enc) {
  if (!enc) return 'utf8';
  var retried;
  while (true) {
    switch (enc) {
      case 'utf8':
      case 'utf-8':
        return 'utf8';
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return 'utf16le';
      case 'latin1':
      case 'binary':
        return 'latin1';
      case 'base64':
      case 'ascii':
      case 'hex':
        return enc;
      default:
        if (retried) return; // undefined
        enc = ('' + enc).toLowerCase();
        retried = true;
    }
  }
};

// Do not cache `Buffer.isEncoding` when checking encoding names as some
// modules monkey-patch it to support additional encodings
function normalizeEncoding(enc) {
  var nenc = _normalizeEncoding(enc);
  if (typeof nenc !== 'string' && (Buffer.isEncoding === isEncoding || !isEncoding(enc))) throw new Error('Unknown encoding: ' + enc);
  return nenc || enc;
}

// StringDecoder provides an interface for efficiently splitting a series of
// buffers into a series of JS strings without breaking apart multi-byte
// characters.
exports.I = StringDecoder;
function StringDecoder(encoding) {
  this.encoding = normalizeEncoding(encoding);
  var nb;
  switch (this.encoding) {
    case 'utf16le':
      this.text = utf16Text;
      this.end = utf16End;
      nb = 4;
      break;
    case 'utf8':
      this.fillLast = utf8FillLast;
      nb = 4;
      break;
    case 'base64':
      this.text = base64Text;
      this.end = base64End;
      nb = 3;
      break;
    default:
      this.write = simpleWrite;
      this.end = simpleEnd;
      return;
  }
  this.lastNeed = 0;
  this.lastTotal = 0;
  this.lastChar = Buffer.allocUnsafe(nb);
}

StringDecoder.prototype.write = function (buf) {
  if (buf.length === 0) return '';
  var r;
  var i;
  if (this.lastNeed) {
    r = this.fillLast(buf);
    if (r === undefined) return '';
    i = this.lastNeed;
    this.lastNeed = 0;
  } else {
    i = 0;
  }
  if (i < buf.length) return r ? r + this.text(buf, i) : this.text(buf, i);
  return r || '';
};

StringDecoder.prototype.end = utf8End;

// Returns only complete characters in a Buffer
StringDecoder.prototype.text = utf8Text;

// Attempts to complete a partial non-UTF-8 character using bytes from a Buffer
StringDecoder.prototype.fillLast = function (buf) {
  if (this.lastNeed <= buf.length) {
    buf.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, this.lastNeed);
    return this.lastChar.toString(this.encoding, 0, this.lastTotal);
  }
  buf.copy(this.lastChar, this.lastTotal - this.lastNeed, 0, buf.length);
  this.lastNeed -= buf.length;
};

// Checks the type of a UTF-8 byte, whether it's ASCII, a leading byte, or a
// continuation byte. If an invalid byte is detected, -2 is returned.
function utf8CheckByte(byte) {
  if (byte <= 0x7F) return 0;else if (byte >> 5 === 0x06) return 2;else if (byte >> 4 === 0x0E) return 3;else if (byte >> 3 === 0x1E) return 4;
  return byte >> 6 === 0x02 ? -1 : -2;
}

// Checks at most 3 bytes at the end of a Buffer in order to detect an
// incomplete multi-byte UTF-8 character. The total number of bytes (2, 3, or 4)
// needed to complete the UTF-8 character (if applicable) are returned.
function utf8CheckIncomplete(self, buf, i) {
  var j = buf.length - 1;
  if (j < i) return 0;
  var nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) self.lastNeed = nb - 1;
    return nb;
  }
  if (--j < i || nb === -2) return 0;
  nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) self.lastNeed = nb - 2;
    return nb;
  }
  if (--j < i || nb === -2) return 0;
  nb = utf8CheckByte(buf[j]);
  if (nb >= 0) {
    if (nb > 0) {
      if (nb === 2) nb = 0;else self.lastNeed = nb - 3;
    }
    return nb;
  }
  return 0;
}

// Validates as many continuation bytes for a multi-byte UTF-8 character as
// needed or are available. If we see a non-continuation byte where we expect
// one, we "replace" the validated continuation bytes we've seen so far with
// a single UTF-8 replacement character ('\ufffd'), to match v8's UTF-8 decoding
// behavior. The continuation byte check is included three times in the case
// where all of the continuation bytes for a character exist in the same buffer.
// It is also done this way as a slight performance increase instead of using a
// loop.
function utf8CheckExtraBytes(self, buf, p) {
  if ((buf[0] & 0xC0) !== 0x80) {
    self.lastNeed = 0;
    return '\ufffd';
  }
  if (self.lastNeed > 1 && buf.length > 1) {
    if ((buf[1] & 0xC0) !== 0x80) {
      self.lastNeed = 1;
      return '\ufffd';
    }
    if (self.lastNeed > 2 && buf.length > 2) {
      if ((buf[2] & 0xC0) !== 0x80) {
        self.lastNeed = 2;
        return '\ufffd';
      }
    }
  }
}

// Attempts to complete a multi-byte UTF-8 character using bytes from a Buffer.
function utf8FillLast(buf) {
  var p = this.lastTotal - this.lastNeed;
  var r = utf8CheckExtraBytes(this, buf, p);
  if (r !== undefined) return r;
  if (this.lastNeed <= buf.length) {
    buf.copy(this.lastChar, p, 0, this.lastNeed);
    return this.lastChar.toString(this.encoding, 0, this.lastTotal);
  }
  buf.copy(this.lastChar, p, 0, buf.length);
  this.lastNeed -= buf.length;
}

// Returns all complete UTF-8 characters in a Buffer. If the Buffer ended on a
// partial character, the character's bytes are buffered until the required
// number of bytes are available.
function utf8Text(buf, i) {
  var total = utf8CheckIncomplete(this, buf, i);
  if (!this.lastNeed) return buf.toString('utf8', i);
  this.lastTotal = total;
  var end = buf.length - (total - this.lastNeed);
  buf.copy(this.lastChar, 0, end);
  return buf.toString('utf8', i, end);
}

// For UTF-8, a replacement character is added when ending on a partial
// character.
function utf8End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) return r + '\ufffd';
  return r;
}

// UTF-16LE typically needs two bytes per character, but even if we have an even
// number of bytes available, we need to check if we end on a leading/high
// surrogate. In that case, we need to wait for the next two bytes in order to
// decode the last character properly.
function utf16Text(buf, i) {
  if ((buf.length - i) % 2 === 0) {
    var r = buf.toString('utf16le', i);
    if (r) {
      var c = r.charCodeAt(r.length - 1);
      if (c >= 0xD800 && c <= 0xDBFF) {
        this.lastNeed = 2;
        this.lastTotal = 4;
        this.lastChar[0] = buf[buf.length - 2];
        this.lastChar[1] = buf[buf.length - 1];
        return r.slice(0, -1);
      }
    }
    return r;
  }
  this.lastNeed = 1;
  this.lastTotal = 2;
  this.lastChar[0] = buf[buf.length - 1];
  return buf.toString('utf16le', i, buf.length - 1);
}

// For UTF-16LE we do not explicitly append special replacement characters if we
// end on a partial character, we simply let v8 handle that.
function utf16End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) {
    var end = this.lastTotal - this.lastNeed;
    return r + this.lastChar.toString('utf16le', 0, end);
  }
  return r;
}

function base64Text(buf, i) {
  var n = (buf.length - i) % 3;
  if (n === 0) return buf.toString('base64', i);
  this.lastNeed = 3 - n;
  this.lastTotal = 3;
  if (n === 1) {
    this.lastChar[0] = buf[buf.length - 1];
  } else {
    this.lastChar[0] = buf[buf.length - 2];
    this.lastChar[1] = buf[buf.length - 1];
  }
  return buf.toString('base64', i, buf.length - n);
}

function base64End(buf) {
  var r = buf && buf.length ? this.write(buf) : '';
  if (this.lastNeed) return r + this.lastChar.toString('base64', 0, 3 - this.lastNeed);
  return r;
}

// Pass bytes on through for single-byte encodings (e.g. ascii, latin1, hex)
function simpleWrite(buf) {
  return buf.toString(this.encoding);
}

function simpleEnd(buf) {
  return buf && buf.length ? this.write(buf) : '';
}

/***/ }),

/***/ 3483:
/***/ (function(module) {

var TINF_OK = 0;
var TINF_DATA_ERROR = -3;

function Tree() {
  this.table = new Uint16Array(16);   /* table of code length counts */
  this.trans = new Uint16Array(288);  /* code -> symbol translation table */
}

function Data(source, dest) {
  this.source = source;
  this.sourceIndex = 0;
  this.tag = 0;
  this.bitcount = 0;
  
  this.dest = dest;
  this.destLen = 0;
  
  this.ltree = new Tree();  /* dynamic length/symbol tree */
  this.dtree = new Tree();  /* dynamic distance tree */
}

/* --------------------------------------------------- *
 * -- uninitialized global data (static structures) -- *
 * --------------------------------------------------- */

var sltree = new Tree();
var sdtree = new Tree();

/* extra bits and base tables for length codes */
var length_bits = new Uint8Array(30);
var length_base = new Uint16Array(30);

/* extra bits and base tables for distance codes */
var dist_bits = new Uint8Array(30);
var dist_base = new Uint16Array(30);

/* special ordering of code length codes */
var clcidx = new Uint8Array([
  16, 17, 18, 0, 8, 7, 9, 6,
  10, 5, 11, 4, 12, 3, 13, 2,
  14, 1, 15
]);

/* used by tinf_decode_trees, avoids allocations every call */
var code_tree = new Tree();
var lengths = new Uint8Array(288 + 32);

/* ----------------------- *
 * -- utility functions -- *
 * ----------------------- */

/* build extra bits and base tables */
function tinf_build_bits_base(bits, base, delta, first) {
  var i, sum;

  /* build bits table */
  for (i = 0; i < delta; ++i) bits[i] = 0;
  for (i = 0; i < 30 - delta; ++i) bits[i + delta] = i / delta | 0;

  /* build base table */
  for (sum = first, i = 0; i < 30; ++i) {
    base[i] = sum;
    sum += 1 << bits[i];
  }
}

/* build the fixed huffman trees */
function tinf_build_fixed_trees(lt, dt) {
  var i;

  /* build fixed length tree */
  for (i = 0; i < 7; ++i) lt.table[i] = 0;

  lt.table[7] = 24;
  lt.table[8] = 152;
  lt.table[9] = 112;

  for (i = 0; i < 24; ++i) lt.trans[i] = 256 + i;
  for (i = 0; i < 144; ++i) lt.trans[24 + i] = i;
  for (i = 0; i < 8; ++i) lt.trans[24 + 144 + i] = 280 + i;
  for (i = 0; i < 112; ++i) lt.trans[24 + 144 + 8 + i] = 144 + i;

  /* build fixed distance tree */
  for (i = 0; i < 5; ++i) dt.table[i] = 0;

  dt.table[5] = 32;

  for (i = 0; i < 32; ++i) dt.trans[i] = i;
}

/* given an array of code lengths, build a tree */
var offs = new Uint16Array(16);

function tinf_build_tree(t, lengths, off, num) {
  var i, sum;

  /* clear code length count table */
  for (i = 0; i < 16; ++i) t.table[i] = 0;

  /* scan symbol lengths, and sum code length counts */
  for (i = 0; i < num; ++i) t.table[lengths[off + i]]++;

  t.table[0] = 0;

  /* compute offset table for distribution sort */
  for (sum = 0, i = 0; i < 16; ++i) {
    offs[i] = sum;
    sum += t.table[i];
  }

  /* create code->symbol translation table (symbols sorted by code) */
  for (i = 0; i < num; ++i) {
    if (lengths[off + i]) t.trans[offs[lengths[off + i]]++] = i;
  }
}

/* ---------------------- *
 * -- decode functions -- *
 * ---------------------- */

/* get one bit from source stream */
function tinf_getbit(d) {
  /* check if tag is empty */
  if (!d.bitcount--) {
    /* load next tag */
    d.tag = d.source[d.sourceIndex++];
    d.bitcount = 7;
  }

  /* shift bit out of tag */
  var bit = d.tag & 1;
  d.tag >>>= 1;

  return bit;
}

/* read a num bit value from a stream and add base */
function tinf_read_bits(d, num, base) {
  if (!num)
    return base;

  while (d.bitcount < 24) {
    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
    d.bitcount += 8;
  }

  var val = d.tag & (0xffff >>> (16 - num));
  d.tag >>>= num;
  d.bitcount -= num;
  return val + base;
}

/* given a data stream and a tree, decode a symbol */
function tinf_decode_symbol(d, t) {
  while (d.bitcount < 24) {
    d.tag |= d.source[d.sourceIndex++] << d.bitcount;
    d.bitcount += 8;
  }
  
  var sum = 0, cur = 0, len = 0;
  var tag = d.tag;

  /* get more bits while code value is above sum */
  do {
    cur = 2 * cur + (tag & 1);
    tag >>>= 1;
    ++len;

    sum += t.table[len];
    cur -= t.table[len];
  } while (cur >= 0);
  
  d.tag = tag;
  d.bitcount -= len;

  return t.trans[sum + cur];
}

/* given a data stream, decode dynamic trees from it */
function tinf_decode_trees(d, lt, dt) {
  var hlit, hdist, hclen;
  var i, num, length;

  /* get 5 bits HLIT (257-286) */
  hlit = tinf_read_bits(d, 5, 257);

  /* get 5 bits HDIST (1-32) */
  hdist = tinf_read_bits(d, 5, 1);

  /* get 4 bits HCLEN (4-19) */
  hclen = tinf_read_bits(d, 4, 4);

  for (i = 0; i < 19; ++i) lengths[i] = 0;

  /* read code lengths for code length alphabet */
  for (i = 0; i < hclen; ++i) {
    /* get 3 bits code length (0-7) */
    var clen = tinf_read_bits(d, 3, 0);
    lengths[clcidx[i]] = clen;
  }

  /* build code length tree */
  tinf_build_tree(code_tree, lengths, 0, 19);

  /* decode code lengths for the dynamic trees */
  for (num = 0; num < hlit + hdist;) {
    var sym = tinf_decode_symbol(d, code_tree);

    switch (sym) {
      case 16:
        /* copy previous code length 3-6 times (read 2 bits) */
        var prev = lengths[num - 1];
        for (length = tinf_read_bits(d, 2, 3); length; --length) {
          lengths[num++] = prev;
        }
        break;
      case 17:
        /* repeat code length 0 for 3-10 times (read 3 bits) */
        for (length = tinf_read_bits(d, 3, 3); length; --length) {
          lengths[num++] = 0;
        }
        break;
      case 18:
        /* repeat code length 0 for 11-138 times (read 7 bits) */
        for (length = tinf_read_bits(d, 7, 11); length; --length) {
          lengths[num++] = 0;
        }
        break;
      default:
        /* values 0-15 represent the actual code lengths */
        lengths[num++] = sym;
        break;
    }
  }

  /* build dynamic trees */
  tinf_build_tree(lt, lengths, 0, hlit);
  tinf_build_tree(dt, lengths, hlit, hdist);
}

/* ----------------------------- *
 * -- block inflate functions -- *
 * ----------------------------- */

/* given a stream and two trees, inflate a block of data */
function tinf_inflate_block_data(d, lt, dt) {
  while (1) {
    var sym = tinf_decode_symbol(d, lt);

    /* check for end of block */
    if (sym === 256) {
      return TINF_OK;
    }

    if (sym < 256) {
      d.dest[d.destLen++] = sym;
    } else {
      var length, dist, offs;
      var i;

      sym -= 257;

      /* possibly get more bits from length code */
      length = tinf_read_bits(d, length_bits[sym], length_base[sym]);

      dist = tinf_decode_symbol(d, dt);

      /* possibly get more bits from distance code */
      offs = d.destLen - tinf_read_bits(d, dist_bits[dist], dist_base[dist]);

      /* copy match */
      for (i = offs; i < offs + length; ++i) {
        d.dest[d.destLen++] = d.dest[i];
      }
    }
  }
}

/* inflate an uncompressed block of data */
function tinf_inflate_uncompressed_block(d) {
  var length, invlength;
  var i;
  
  /* unread from bitbuffer */
  while (d.bitcount > 8) {
    d.sourceIndex--;
    d.bitcount -= 8;
  }

  /* get length */
  length = d.source[d.sourceIndex + 1];
  length = 256 * length + d.source[d.sourceIndex];

  /* get one's complement of length */
  invlength = d.source[d.sourceIndex + 3];
  invlength = 256 * invlength + d.source[d.sourceIndex + 2];

  /* check length */
  if (length !== (~invlength & 0x0000ffff))
    return TINF_DATA_ERROR;

  d.sourceIndex += 4;

  /* copy block */
  for (i = length; i; --i)
    d.dest[d.destLen++] = d.source[d.sourceIndex++];

  /* make sure we start next block on a byte boundary */
  d.bitcount = 0;

  return TINF_OK;
}

/* inflate stream from source to dest */
function tinf_uncompress(source, dest) {
  var d = new Data(source, dest);
  var bfinal, btype, res;

  do {
    /* read final block flag */
    bfinal = tinf_getbit(d);

    /* read block type (2 bits) */
    btype = tinf_read_bits(d, 2, 0);

    /* decompress block */
    switch (btype) {
      case 0:
        /* decompress uncompressed block */
        res = tinf_inflate_uncompressed_block(d);
        break;
      case 1:
        /* decompress block with fixed huffman trees */
        res = tinf_inflate_block_data(d, sltree, sdtree);
        break;
      case 2:
        /* decompress block with dynamic huffman trees */
        tinf_decode_trees(d, d.ltree, d.dtree);
        res = tinf_inflate_block_data(d, d.ltree, d.dtree);
        break;
      default:
        res = TINF_DATA_ERROR;
    }

    if (res !== TINF_OK)
      throw new Error('Data error');

  } while (!bfinal);

  if (d.destLen < d.dest.length) {
    if (typeof d.dest.slice === 'function')
      return d.dest.slice(0, d.destLen);
    else
      return d.dest.subarray(0, d.destLen);
  }
  
  return d.dest;
}

/* -------------------- *
 * -- initialization -- *
 * -------------------- */

/* build fixed huffman trees */
tinf_build_fixed_trees(sltree, sdtree);

/* build extra bits and base tables */
tinf_build_bits_base(length_bits, length_base, 4, 3);
tinf_build_bits_base(dist_bits, dist_base, 2, 1);

/* fix a special case */
length_bits[28] = 0;
length_base[28] = 258;

module.exports = tinf_uncompress;


/***/ }),

/***/ 16465:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {


/**
 * Module exports.
 */

module.exports = deprecate;

/**
 * Mark that a method should not be used.
 * Returns a modified function which warns once by default.
 *
 * If `localStorage.noDeprecation = true` is set, then it is a no-op.
 *
 * If `localStorage.throwDeprecation = true` is set, then deprecated functions
 * will throw an Error when invoked.
 *
 * If `localStorage.traceDeprecation = true` is set, then deprecated functions
 * will invoke `console.trace()` instead of `console.error()`.
 *
 * @param {Function} fn - the function to deprecate
 * @param {String} msg - the string to print to the console when `fn` is invoked
 * @returns {Function} a new "deprecated" version of `fn`
 * @api public
 */

function deprecate (fn, msg) {
  if (config('noDeprecation')) {
    return fn;
  }

  var warned = false;
  function deprecated() {
    if (!warned) {
      if (config('throwDeprecation')) {
        throw new Error(msg);
      } else if (config('traceDeprecation')) {
        console.trace(msg);
      } else {
        console.warn(msg);
      }
      warned = true;
    }
    return fn.apply(this, arguments);
  }

  return deprecated;
}

/**
 * Checks `localStorage` for boolean values for the given `name`.
 *
 * @param {String} name
 * @returns {Boolean}
 * @api private
 */

function config (name) {
  // accessing global.localStorage can trigger a DOMException in sandboxed iframes
  try {
    if (!__webpack_require__.g.localStorage) return false;
  } catch (_) {
    return false;
  }
  var val = __webpack_require__.g.localStorage[name];
  if (null == val) return false;
  return String(val).toLowerCase() === 'true';
}


/***/ }),

/***/ 41201:
/***/ (function(module) {

module.exports = function isBuffer(arg) {
  return arg && typeof arg === 'object'
    && typeof arg.copy === 'function'
    && typeof arg.fill === 'function'
    && typeof arg.readUInt8 === 'function';
}

/***/ }),

/***/ 29490:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

"use strict";
// Currently in sync with Node.js lib/internal/util/types.js
// https://github.com/nodejs/node/commit/112cc7c27551254aa2b17098fb774867f05ed0d9



var isArgumentsObject = __webpack_require__(67906);
var isGeneratorFunction = __webpack_require__(44610);
var whichTypedArray = __webpack_require__(43381);
var isTypedArray = __webpack_require__(46094);

function uncurryThis(f) {
  return f.call.bind(f);
}

var BigIntSupported = typeof BigInt !== 'undefined';
var SymbolSupported = typeof Symbol !== 'undefined';

var ObjectToString = uncurryThis(Object.prototype.toString);

var numberValue = uncurryThis(Number.prototype.valueOf);
var stringValue = uncurryThis(String.prototype.valueOf);
var booleanValue = uncurryThis(Boolean.prototype.valueOf);

if (BigIntSupported) {
  var bigIntValue = uncurryThis(BigInt.prototype.valueOf);
}

if (SymbolSupported) {
  var symbolValue = uncurryThis(Symbol.prototype.valueOf);
}

function checkBoxedPrimitive(value, prototypeValueOf) {
  if (typeof value !== 'object') {
    return false;
  }
  try {
    prototypeValueOf(value);
    return true;
  } catch(e) {
    return false;
  }
}

exports.isArgumentsObject = isArgumentsObject;
exports.isGeneratorFunction = isGeneratorFunction;
exports.isTypedArray = isTypedArray;

// Taken from here and modified for better browser support
// https://github.com/sindresorhus/p-is-promise/blob/cda35a513bda03f977ad5cde3a079d237e82d7ef/index.js
function isPromise(input) {
	return (
		(
			typeof Promise !== 'undefined' &&
			input instanceof Promise
		) ||
		(
			input !== null &&
			typeof input === 'object' &&
			typeof input.then === 'function' &&
			typeof input.catch === 'function'
		)
	);
}
exports.isPromise = isPromise;

function isArrayBufferView(value) {
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView) {
    return ArrayBuffer.isView(value);
  }

  return (
    isTypedArray(value) ||
    isDataView(value)
  );
}
exports.isArrayBufferView = isArrayBufferView;


function isUint8Array(value) {
  return whichTypedArray(value) === 'Uint8Array';
}
exports.isUint8Array = isUint8Array;

function isUint8ClampedArray(value) {
  return whichTypedArray(value) === 'Uint8ClampedArray';
}
exports.isUint8ClampedArray = isUint8ClampedArray;

function isUint16Array(value) {
  return whichTypedArray(value) === 'Uint16Array';
}
exports.isUint16Array = isUint16Array;

function isUint32Array(value) {
  return whichTypedArray(value) === 'Uint32Array';
}
exports.isUint32Array = isUint32Array;

function isInt8Array(value) {
  return whichTypedArray(value) === 'Int8Array';
}
exports.isInt8Array = isInt8Array;

function isInt16Array(value) {
  return whichTypedArray(value) === 'Int16Array';
}
exports.isInt16Array = isInt16Array;

function isInt32Array(value) {
  return whichTypedArray(value) === 'Int32Array';
}
exports.isInt32Array = isInt32Array;

function isFloat32Array(value) {
  return whichTypedArray(value) === 'Float32Array';
}
exports.isFloat32Array = isFloat32Array;

function isFloat64Array(value) {
  return whichTypedArray(value) === 'Float64Array';
}
exports.isFloat64Array = isFloat64Array;

function isBigInt64Array(value) {
  return whichTypedArray(value) === 'BigInt64Array';
}
exports.isBigInt64Array = isBigInt64Array;

function isBigUint64Array(value) {
  return whichTypedArray(value) === 'BigUint64Array';
}
exports.isBigUint64Array = isBigUint64Array;

function isMapToString(value) {
  return ObjectToString(value) === '[object Map]';
}
isMapToString.working = (
  typeof Map !== 'undefined' &&
  isMapToString(new Map())
);

function isMap(value) {
  if (typeof Map === 'undefined') {
    return false;
  }

  return isMapToString.working
    ? isMapToString(value)
    : value instanceof Map;
}
exports.isMap = isMap;

function isSetToString(value) {
  return ObjectToString(value) === '[object Set]';
}
isSetToString.working = (
  typeof Set !== 'undefined' &&
  isSetToString(new Set())
);
function isSet(value) {
  if (typeof Set === 'undefined') {
    return false;
  }

  return isSetToString.working
    ? isSetToString(value)
    : value instanceof Set;
}
exports.isSet = isSet;

function isWeakMapToString(value) {
  return ObjectToString(value) === '[object WeakMap]';
}
isWeakMapToString.working = (
  typeof WeakMap !== 'undefined' &&
  isWeakMapToString(new WeakMap())
);
function isWeakMap(value) {
  if (typeof WeakMap === 'undefined') {
    return false;
  }

  return isWeakMapToString.working
    ? isWeakMapToString(value)
    : value instanceof WeakMap;
}
exports.isWeakMap = isWeakMap;

function isWeakSetToString(value) {
  return ObjectToString(value) === '[object WeakSet]';
}
isWeakSetToString.working = (
  typeof WeakSet !== 'undefined' &&
  isWeakSetToString(new WeakSet())
);
function isWeakSet(value) {
  return isWeakSetToString(value);
}
exports.isWeakSet = isWeakSet;

function isArrayBufferToString(value) {
  return ObjectToString(value) === '[object ArrayBuffer]';
}
isArrayBufferToString.working = (
  typeof ArrayBuffer !== 'undefined' &&
  isArrayBufferToString(new ArrayBuffer())
);
function isArrayBuffer(value) {
  if (typeof ArrayBuffer === 'undefined') {
    return false;
  }

  return isArrayBufferToString.working
    ? isArrayBufferToString(value)
    : value instanceof ArrayBuffer;
}
exports.isArrayBuffer = isArrayBuffer;

function isDataViewToString(value) {
  return ObjectToString(value) === '[object DataView]';
}
isDataViewToString.working = (
  typeof ArrayBuffer !== 'undefined' &&
  typeof DataView !== 'undefined' &&
  isDataViewToString(new DataView(new ArrayBuffer(1), 0, 1))
);
function isDataView(value) {
  if (typeof DataView === 'undefined') {
    return false;
  }

  return isDataViewToString.working
    ? isDataViewToString(value)
    : value instanceof DataView;
}
exports.isDataView = isDataView;

// Store a copy of SharedArrayBuffer in case it's deleted elsewhere
var SharedArrayBufferCopy = typeof SharedArrayBuffer !== 'undefined' ? SharedArrayBuffer : undefined;
function isSharedArrayBufferToString(value) {
  return ObjectToString(value) === '[object SharedArrayBuffer]';
}
function isSharedArrayBuffer(value) {
  if (typeof SharedArrayBufferCopy === 'undefined') {
    return false;
  }

  if (typeof isSharedArrayBufferToString.working === 'undefined') {
    isSharedArrayBufferToString.working = isSharedArrayBufferToString(new SharedArrayBufferCopy());
  }

  return isSharedArrayBufferToString.working
    ? isSharedArrayBufferToString(value)
    : value instanceof SharedArrayBufferCopy;
}
exports.isSharedArrayBuffer = isSharedArrayBuffer;

function isAsyncFunction(value) {
  return ObjectToString(value) === '[object AsyncFunction]';
}
exports.isAsyncFunction = isAsyncFunction;

function isMapIterator(value) {
  return ObjectToString(value) === '[object Map Iterator]';
}
exports.isMapIterator = isMapIterator;

function isSetIterator(value) {
  return ObjectToString(value) === '[object Set Iterator]';
}
exports.isSetIterator = isSetIterator;

function isGeneratorObject(value) {
  return ObjectToString(value) === '[object Generator]';
}
exports.isGeneratorObject = isGeneratorObject;

function isWebAssemblyCompiledModule(value) {
  return ObjectToString(value) === '[object WebAssembly.Module]';
}
exports.isWebAssemblyCompiledModule = isWebAssemblyCompiledModule;

function isNumberObject(value) {
  return checkBoxedPrimitive(value, numberValue);
}
exports.isNumberObject = isNumberObject;

function isStringObject(value) {
  return checkBoxedPrimitive(value, stringValue);
}
exports.isStringObject = isStringObject;

function isBooleanObject(value) {
  return checkBoxedPrimitive(value, booleanValue);
}
exports.isBooleanObject = isBooleanObject;

function isBigIntObject(value) {
  return BigIntSupported && checkBoxedPrimitive(value, bigIntValue);
}
exports.isBigIntObject = isBigIntObject;

function isSymbolObject(value) {
  return SymbolSupported && checkBoxedPrimitive(value, symbolValue);
}
exports.isSymbolObject = isSymbolObject;

function isBoxedPrimitive(value) {
  return (
    isNumberObject(value) ||
    isStringObject(value) ||
    isBooleanObject(value) ||
    isBigIntObject(value) ||
    isSymbolObject(value)
  );
}
exports.isBoxedPrimitive = isBoxedPrimitive;

function isAnyArrayBuffer(value) {
  return typeof Uint8Array !== 'undefined' && (
    isArrayBuffer(value) ||
    isSharedArrayBuffer(value)
  );
}
exports.isAnyArrayBuffer = isAnyArrayBuffer;

['isProxy', 'isExternal', 'isModuleNamespaceObject'].forEach(function(method) {
  Object.defineProperty(exports, method, {
    enumerable: false,
    value: function() {
      throw new Error(method + ' is not supported in userland');
    }
  });
});


/***/ }),

/***/ 7187:
/***/ (function(__unused_webpack_module, exports, __webpack_require__) {

/* provided dependency */ var process = __webpack_require__(9964);
// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

var getOwnPropertyDescriptors = Object.getOwnPropertyDescriptors ||
  function getOwnPropertyDescriptors(obj) {
    var keys = Object.keys(obj);
    var descriptors = {};
    for (var i = 0; i < keys.length; i++) {
      descriptors[keys[i]] = Object.getOwnPropertyDescriptor(obj, keys[i]);
    }
    return descriptors;
  };

var formatRegExp = /%[sdj%]/g;
exports.format = function(f) {
  if (!isString(f)) {
    var objects = [];
    for (var i = 0; i < arguments.length; i++) {
      objects.push(inspect(arguments[i]));
    }
    return objects.join(' ');
  }

  var i = 1;
  var args = arguments;
  var len = args.length;
  var str = String(f).replace(formatRegExp, function(x) {
    if (x === '%%') return '%';
    if (i >= len) return x;
    switch (x) {
      case '%s': return String(args[i++]);
      case '%d': return Number(args[i++]);
      case '%j':
        try {
          return JSON.stringify(args[i++]);
        } catch (_) {
          return '[Circular]';
        }
      default:
        return x;
    }
  });
  for (var x = args[i]; i < len; x = args[++i]) {
    if (isNull(x) || !isObject(x)) {
      str += ' ' + x;
    } else {
      str += ' ' + inspect(x);
    }
  }
  return str;
};


// Mark that a method should not be used.
// Returns a modified function which warns once by default.
// If --no-deprecation is set, then it is a no-op.
exports.deprecate = function(fn, msg) {
  if (typeof process !== 'undefined' && process.noDeprecation === true) {
    return fn;
  }

  // Allow for deprecating things in the process of starting up.
  if (typeof process === 'undefined') {
    return function() {
      return exports.deprecate(fn, msg).apply(this, arguments);
    };
  }

  var warned = false;
  function deprecated() {
    if (!warned) {
      if (process.throwDeprecation) {
        throw new Error(msg);
      } else if (process.traceDeprecation) {
        console.trace(msg);
      } else {
        console.error(msg);
      }
      warned = true;
    }
    return fn.apply(this, arguments);
  }

  return deprecated;
};


var debugs = {};
var debugEnvRegex = /^$/;

if (process.env.NODE_DEBUG) {
  var debugEnv = process.env.NODE_DEBUG;
  debugEnv = debugEnv.replace(/[|\\{}()[\]^$+?.]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/,/g, '$|^')
    .toUpperCase();
  debugEnvRegex = new RegExp('^' + debugEnv + '$', 'i');
}
exports.debuglog = function(set) {
  set = set.toUpperCase();
  if (!debugs[set]) {
    if (debugEnvRegex.test(set)) {
      var pid = process.pid;
      debugs[set] = function() {
        var msg = exports.format.apply(exports, arguments);
        console.error('%s %d: %s', set, pid, msg);
      };
    } else {
      debugs[set] = function() {};
    }
  }
  return debugs[set];
};


/**
 * Echos the value of a value. Trys to print the value out
 * in the best way possible given the different types.
 *
 * @param {Object} obj The object to print out.
 * @param {Object} opts Optional options object that alters the output.
 */
/* legacy: obj, showHidden, depth, colors*/
function inspect(obj, opts) {
  // default options
  var ctx = {
    seen: [],
    stylize: stylizeNoColor
  };
  // legacy...
  if (arguments.length >= 3) ctx.depth = arguments[2];
  if (arguments.length >= 4) ctx.colors = arguments[3];
  if (isBoolean(opts)) {
    // legacy...
    ctx.showHidden = opts;
  } else if (opts) {
    // got an "options" object
    exports._extend(ctx, opts);
  }
  // set default options
  if (isUndefined(ctx.showHidden)) ctx.showHidden = false;
  if (isUndefined(ctx.depth)) ctx.depth = 2;
  if (isUndefined(ctx.colors)) ctx.colors = false;
  if (isUndefined(ctx.customInspect)) ctx.customInspect = true;
  if (ctx.colors) ctx.stylize = stylizeWithColor;
  return formatValue(ctx, obj, ctx.depth);
}
exports.inspect = inspect;


// http://en.wikipedia.org/wiki/ANSI_escape_code#graphics
inspect.colors = {
  'bold' : [1, 22],
  'italic' : [3, 23],
  'underline' : [4, 24],
  'inverse' : [7, 27],
  'white' : [37, 39],
  'grey' : [90, 39],
  'black' : [30, 39],
  'blue' : [34, 39],
  'cyan' : [36, 39],
  'green' : [32, 39],
  'magenta' : [35, 39],
  'red' : [31, 39],
  'yellow' : [33, 39]
};

// Don't use 'blue' not visible on cmd.exe
inspect.styles = {
  'special': 'cyan',
  'number': 'yellow',
  'boolean': 'yellow',
  'undefined': 'grey',
  'null': 'bold',
  'string': 'green',
  'date': 'magenta',
  // "name": intentionally not styling
  'regexp': 'red'
};


function stylizeWithColor(str, styleType) {
  var style = inspect.styles[styleType];

  if (style) {
    return '\u001b[' + inspect.colors[style][0] + 'm' + str +
           '\u001b[' + inspect.colors[style][1] + 'm';
  } else {
    return str;
  }
}


function stylizeNoColor(str, styleType) {
  return str;
}


function arrayToHash(array) {
  var hash = {};

  array.forEach(function(val, idx) {
    hash[val] = true;
  });

  return hash;
}


function formatValue(ctx, value, recurseTimes) {
  // Provide a hook for user-specified inspect functions.
  // Check that value is an object with an inspect function on it
  if (ctx.customInspect &&
      value &&
      isFunction(value.inspect) &&
      // Filter out the util module, it's inspect function is special
      value.inspect !== exports.inspect &&
      // Also filter out any prototype objects using the circular check.
      !(value.constructor && value.constructor.prototype === value)) {
    var ret = value.inspect(recurseTimes, ctx);
    if (!isString(ret)) {
      ret = formatValue(ctx, ret, recurseTimes);
    }
    return ret;
  }

  // Primitive types cannot have properties
  var primitive = formatPrimitive(ctx, value);
  if (primitive) {
    return primitive;
  }

  // Look up the keys of the object.
  var keys = Object.keys(value);
  var visibleKeys = arrayToHash(keys);

  if (ctx.showHidden) {
    keys = Object.getOwnPropertyNames(value);
  }

  // IE doesn't make error fields non-enumerable
  // http://msdn.microsoft.com/en-us/library/ie/dww52sbt(v=vs.94).aspx
  if (isError(value)
      && (keys.indexOf('message') >= 0 || keys.indexOf('description') >= 0)) {
    return formatError(value);
  }

  // Some type of object without properties can be shortcutted.
  if (keys.length === 0) {
    if (isFunction(value)) {
      var name = value.name ? ': ' + value.name : '';
      return ctx.stylize('[Function' + name + ']', 'special');
    }
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    }
    if (isDate(value)) {
      return ctx.stylize(Date.prototype.toString.call(value), 'date');
    }
    if (isError(value)) {
      return formatError(value);
    }
  }

  var base = '', array = false, braces = ['{', '}'];

  // Make Array say that they are Array
  if (isArray(value)) {
    array = true;
    braces = ['[', ']'];
  }

  // Make functions say that they are functions
  if (isFunction(value)) {
    var n = value.name ? ': ' + value.name : '';
    base = ' [Function' + n + ']';
  }

  // Make RegExps say that they are RegExps
  if (isRegExp(value)) {
    base = ' ' + RegExp.prototype.toString.call(value);
  }

  // Make dates with properties first say the date
  if (isDate(value)) {
    base = ' ' + Date.prototype.toUTCString.call(value);
  }

  // Make error with message first say the error
  if (isError(value)) {
    base = ' ' + formatError(value);
  }

  if (keys.length === 0 && (!array || value.length == 0)) {
    return braces[0] + base + braces[1];
  }

  if (recurseTimes < 0) {
    if (isRegExp(value)) {
      return ctx.stylize(RegExp.prototype.toString.call(value), 'regexp');
    } else {
      return ctx.stylize('[Object]', 'special');
    }
  }

  ctx.seen.push(value);

  var output;
  if (array) {
    output = formatArray(ctx, value, recurseTimes, visibleKeys, keys);
  } else {
    output = keys.map(function(key) {
      return formatProperty(ctx, value, recurseTimes, visibleKeys, key, array);
    });
  }

  ctx.seen.pop();

  return reduceToSingleString(output, base, braces);
}


function formatPrimitive(ctx, value) {
  if (isUndefined(value))
    return ctx.stylize('undefined', 'undefined');
  if (isString(value)) {
    var simple = '\'' + JSON.stringify(value).replace(/^"|"$/g, '')
                                             .replace(/'/g, "\\'")
                                             .replace(/\\"/g, '"') + '\'';
    return ctx.stylize(simple, 'string');
  }
  if (isNumber(value))
    return ctx.stylize('' + value, 'number');
  if (isBoolean(value))
    return ctx.stylize('' + value, 'boolean');
  // For some reason typeof null is "object", so special case here.
  if (isNull(value))
    return ctx.stylize('null', 'null');
}


function formatError(value) {
  return '[' + Error.prototype.toString.call(value) + ']';
}


function formatArray(ctx, value, recurseTimes, visibleKeys, keys) {
  var output = [];
  for (var i = 0, l = value.length; i < l; ++i) {
    if (hasOwnProperty(value, String(i))) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          String(i), true));
    } else {
      output.push('');
    }
  }
  keys.forEach(function(key) {
    if (!key.match(/^\d+$/)) {
      output.push(formatProperty(ctx, value, recurseTimes, visibleKeys,
          key, true));
    }
  });
  return output;
}


function formatProperty(ctx, value, recurseTimes, visibleKeys, key, array) {
  var name, str, desc;
  desc = Object.getOwnPropertyDescriptor(value, key) || { value: value[key] };
  if (desc.get) {
    if (desc.set) {
      str = ctx.stylize('[Getter/Setter]', 'special');
    } else {
      str = ctx.stylize('[Getter]', 'special');
    }
  } else {
    if (desc.set) {
      str = ctx.stylize('[Setter]', 'special');
    }
  }
  if (!hasOwnProperty(visibleKeys, key)) {
    name = '[' + key + ']';
  }
  if (!str) {
    if (ctx.seen.indexOf(desc.value) < 0) {
      if (isNull(recurseTimes)) {
        str = formatValue(ctx, desc.value, null);
      } else {
        str = formatValue(ctx, desc.value, recurseTimes - 1);
      }
      if (str.indexOf('\n') > -1) {
        if (array) {
          str = str.split('\n').map(function(line) {
            return '  ' + line;
          }).join('\n').slice(2);
        } else {
          str = '\n' + str.split('\n').map(function(line) {
            return '   ' + line;
          }).join('\n');
        }
      }
    } else {
      str = ctx.stylize('[Circular]', 'special');
    }
  }
  if (isUndefined(name)) {
    if (array && key.match(/^\d+$/)) {
      return str;
    }
    name = JSON.stringify('' + key);
    if (name.match(/^"([a-zA-Z_][a-zA-Z_0-9]*)"$/)) {
      name = name.slice(1, -1);
      name = ctx.stylize(name, 'name');
    } else {
      name = name.replace(/'/g, "\\'")
                 .replace(/\\"/g, '"')
                 .replace(/(^"|"$)/g, "'");
      name = ctx.stylize(name, 'string');
    }
  }

  return name + ': ' + str;
}


function reduceToSingleString(output, base, braces) {
  var numLinesEst = 0;
  var length = output.reduce(function(prev, cur) {
    numLinesEst++;
    if (cur.indexOf('\n') >= 0) numLinesEst++;
    return prev + cur.replace(/\u001b\[\d\d?m/g, '').length + 1;
  }, 0);

  if (length > 60) {
    return braces[0] +
           (base === '' ? '' : base + '\n ') +
           ' ' +
           output.join(',\n  ') +
           ' ' +
           braces[1];
  }

  return braces[0] + base + ' ' + output.join(', ') + ' ' + braces[1];
}


// NOTE: These type checking functions intentionally don't use `instanceof`
// because it is fragile and can be easily faked with `Object.create()`.
exports.types = __webpack_require__(29490);

function isArray(ar) {
  return Array.isArray(ar);
}
exports.isArray = isArray;

function isBoolean(arg) {
  return typeof arg === 'boolean';
}
exports.isBoolean = isBoolean;

function isNull(arg) {
  return arg === null;
}
exports.isNull = isNull;

function isNullOrUndefined(arg) {
  return arg == null;
}
exports.isNullOrUndefined = isNullOrUndefined;

function isNumber(arg) {
  return typeof arg === 'number';
}
exports.isNumber = isNumber;

function isString(arg) {
  return typeof arg === 'string';
}
exports.isString = isString;

function isSymbol(arg) {
  return typeof arg === 'symbol';
}
exports.isSymbol = isSymbol;

function isUndefined(arg) {
  return arg === void 0;
}
exports.isUndefined = isUndefined;

function isRegExp(re) {
  return isObject(re) && objectToString(re) === '[object RegExp]';
}
exports.isRegExp = isRegExp;
exports.types.isRegExp = isRegExp;

function isObject(arg) {
  return typeof arg === 'object' && arg !== null;
}
exports.isObject = isObject;

function isDate(d) {
  return isObject(d) && objectToString(d) === '[object Date]';
}
exports.isDate = isDate;
exports.types.isDate = isDate;

function isError(e) {
  return isObject(e) &&
      (objectToString(e) === '[object Error]' || e instanceof Error);
}
exports.isError = isError;
exports.types.isNativeError = isError;

function isFunction(arg) {
  return typeof arg === 'function';
}
exports.isFunction = isFunction;

function isPrimitive(arg) {
  return arg === null ||
         typeof arg === 'boolean' ||
         typeof arg === 'number' ||
         typeof arg === 'string' ||
         typeof arg === 'symbol' ||  // ES6 symbol
         typeof arg === 'undefined';
}
exports.isPrimitive = isPrimitive;

exports.isBuffer = __webpack_require__(41201);

function objectToString(o) {
  return Object.prototype.toString.call(o);
}


function pad(n) {
  return n < 10 ? '0' + n.toString(10) : n.toString(10);
}


var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
              'Oct', 'Nov', 'Dec'];

// 26 Feb 16:19:34
function timestamp() {
  var d = new Date();
  var time = [pad(d.getHours()),
              pad(d.getMinutes()),
              pad(d.getSeconds())].join(':');
  return [d.getDate(), months[d.getMonth()], time].join(' ');
}


// log is just a thin wrapper to console.log that prepends a timestamp
exports.log = function() {
  console.log('%s - %s', timestamp(), exports.format.apply(exports, arguments));
};


/**
 * Inherit the prototype methods from one constructor into another.
 *
 * The Function.prototype.inherits from lang.js rewritten as a standalone
 * function (not on Function.prototype). NOTE: If this file is to be loaded
 * during bootstrapping this function needs to be rewritten using some native
 * functions as prototype setup using normal JavaScript does not work as
 * expected during bootstrapping (see mirror.js in r114903).
 *
 * @param {function} ctor Constructor function which needs to inherit the
 *     prototype.
 * @param {function} superCtor Constructor function to inherit prototype from.
 */
exports.inherits = __webpack_require__(89784);

exports._extend = function(origin, add) {
  // Don't do anything if add isn't an object
  if (!add || !isObject(add)) return origin;

  var keys = Object.keys(add);
  var i = keys.length;
  while (i--) {
    origin[keys[i]] = add[keys[i]];
  }
  return origin;
};

function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

var kCustomPromisifiedSymbol = typeof Symbol !== 'undefined' ? Symbol('util.promisify.custom') : undefined;

exports.promisify = function promisify(original) {
  if (typeof original !== 'function')
    throw new TypeError('The "original" argument must be of type Function');

  if (kCustomPromisifiedSymbol && original[kCustomPromisifiedSymbol]) {
    var fn = original[kCustomPromisifiedSymbol];
    if (typeof fn !== 'function') {
      throw new TypeError('The "util.promisify.custom" argument must be of type Function');
    }
    Object.defineProperty(fn, kCustomPromisifiedSymbol, {
      value: fn, enumerable: false, writable: false, configurable: true
    });
    return fn;
  }

  function fn() {
    var promiseResolve, promiseReject;
    var promise = new Promise(function (resolve, reject) {
      promiseResolve = resolve;
      promiseReject = reject;
    });

    var args = [];
    for (var i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }
    args.push(function (err, value) {
      if (err) {
        promiseReject(err);
      } else {
        promiseResolve(value);
      }
    });

    try {
      original.apply(this, args);
    } catch (err) {
      promiseReject(err);
    }

    return promise;
  }

  Object.setPrototypeOf(fn, Object.getPrototypeOf(original));

  if (kCustomPromisifiedSymbol) Object.defineProperty(fn, kCustomPromisifiedSymbol, {
    value: fn, enumerable: false, writable: false, configurable: true
  });
  return Object.defineProperties(
    fn,
    getOwnPropertyDescriptors(original)
  );
}

exports.promisify.custom = kCustomPromisifiedSymbol

function callbackifyOnRejected(reason, cb) {
  // `!reason` guard inspired by bluebird (Ref: https://goo.gl/t5IS6M).
  // Because `null` is a special error value in callbacks which means "no error
  // occurred", we error-wrap so the callback consumer can distinguish between
  // "the promise rejected with null" or "the promise fulfilled with undefined".
  if (!reason) {
    var newReason = new Error('Promise was rejected with a falsy value');
    newReason.reason = reason;
    reason = newReason;
  }
  return cb(reason);
}

function callbackify(original) {
  if (typeof original !== 'function') {
    throw new TypeError('The "original" argument must be of type Function');
  }

  // We DO NOT return the promise as it gives the user a false sense that
  // the promise is actually somehow related to the callback's execution
  // and that the callback throwing will reject the promise.
  function callbackified() {
    var args = [];
    for (var i = 0; i < arguments.length; i++) {
      args.push(arguments[i]);
    }

    var maybeCb = args.pop();
    if (typeof maybeCb !== 'function') {
      throw new TypeError('The last argument must be of type Function');
    }
    var self = this;
    var cb = function() {
      return maybeCb.apply(self, arguments);
    };
    // In true node style we process the callback on `nextTick` with all the
    // implications (stack, `uncaughtException`, `async_hooks`)
    original.apply(this, args)
      .then(function(ret) { process.nextTick(cb.bind(null, null, ret)) },
            function(rej) { process.nextTick(callbackifyOnRejected.bind(null, rej, cb)) });
  }

  Object.setPrototypeOf(callbackified, Object.getPrototypeOf(original));
  Object.defineProperties(callbackified,
                          getOwnPropertyDescriptors(original));
  return callbackified;
}
exports.callbackify = callbackify;


/***/ }),

/***/ 43381:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var forEach = __webpack_require__(68404);
var availableTypedArrays = __webpack_require__(10821);
var callBind = __webpack_require__(26601);
var callBound = __webpack_require__(22774);
var gOPD = __webpack_require__(68109);

/** @type {(O: object) => string} */
var $toString = callBound('Object.prototype.toString');
var hasToStringTag = __webpack_require__(26626)();

var g = typeof globalThis === 'undefined' ? __webpack_require__.g : globalThis;
var typedArrays = availableTypedArrays();

var $slice = callBound('String.prototype.slice');
var getPrototypeOf = Object.getPrototypeOf; // require('getprototypeof');

/** @type {<T = unknown>(array: readonly T[], value: unknown) => number} */
var $indexOf = callBound('Array.prototype.indexOf', true) || function indexOf(array, value) {
	for (var i = 0; i < array.length; i += 1) {
		if (array[i] === value) {
			return i;
		}
	}
	return -1;
};

/** @typedef {(receiver: import('.').TypedArray) => string | typeof Uint8Array.prototype.slice.call | typeof Uint8Array.prototype.set.call} Getter */
/** @type {{ [k in `\$${import('.').TypedArrayName}`]?: Getter } & { __proto__: null }} */
var cache = { __proto__: null };
if (hasToStringTag && gOPD && getPrototypeOf) {
	forEach(typedArrays, function (typedArray) {
		var arr = new g[typedArray]();
		if (Symbol.toStringTag in arr) {
			var proto = getPrototypeOf(arr);
			// @ts-expect-error TS won't narrow inside a closure
			var descriptor = gOPD(proto, Symbol.toStringTag);
			if (!descriptor) {
				var superProto = getPrototypeOf(proto);
				// @ts-expect-error TS won't narrow inside a closure
				descriptor = gOPD(superProto, Symbol.toStringTag);
			}
			// @ts-expect-error TODO: fix
			cache['$' + typedArray] = callBind(descriptor.get);
		}
	});
} else {
	forEach(typedArrays, function (typedArray) {
		var arr = new g[typedArray]();
		var fn = arr.slice || arr.set;
		if (fn) {
			// @ts-expect-error TODO: fix
			cache['$' + typedArray] = callBind(fn);
		}
	});
}

/** @type {(value: object) => false | import('.').TypedArrayName} */
var tryTypedArrays = function tryAllTypedArrays(value) {
	/** @type {ReturnType<typeof tryAllTypedArrays>} */ var found = false;
	forEach(
		// eslint-disable-next-line no-extra-parens
		/** @type {Record<`\$${TypedArrayName}`, Getter>} */ /** @type {any} */ (cache),
		/** @type {(getter: Getter, name: `\$${import('.').TypedArrayName}`) => void} */
		function (getter, typedArray) {
			if (!found) {
				try {
				// @ts-expect-error TODO: fix
					if ('$' + getter(value) === typedArray) {
						found = $slice(typedArray, 1);
					}
				} catch (e) { /**/ }
			}
		}
	);
	return found;
};

/** @type {(value: object) => false | import('.').TypedArrayName} */
var trySlices = function tryAllSlices(value) {
	/** @type {ReturnType<typeof tryAllSlices>} */ var found = false;
	forEach(
		// eslint-disable-next-line no-extra-parens
		/** @type {Record<`\$${TypedArrayName}`, Getter>} */ /** @type {any} */ (cache),
		/** @type {(getter: typeof cache, name: `\$${import('.').TypedArrayName}`) => void} */ function (getter, name) {
			if (!found) {
				try {
					// @ts-expect-error TODO: fix
					getter(value);
					found = $slice(name, 1);
				} catch (e) { /**/ }
			}
		}
	);
	return found;
};

/** @type {import('.')} */
module.exports = function whichTypedArray(value) {
	if (!value || typeof value !== 'object') { return false; }
	if (!hasToStringTag) {
		/** @type {string} */
		var tag = $slice($toString(value), 8, -1);
		if ($indexOf(typedArrays, tag) > -1) {
			return tag;
		}
		if (tag !== 'Object') {
			return false;
		}
		// node < 0.6 hits here on real Typed Arrays
		return trySlices(value);
	}
	if (!gOPD) { return null; } // unknown engine
	return tryTypedArrays(value);
};


/***/ }),

/***/ 52242:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

// This file is just added for convenience so this repository can be
// directly checked out into a project's deps folder
module.exports = __webpack_require__(45349);


/***/ }),

/***/ 45349:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

(function () {
  var sax;

  if (
     true &&
    module.exports &&
    !__webpack_require__.g.xmldocAssumeBrowser
  ) {
    // We're being used in a Node-like environment
    sax = __webpack_require__(61733);
  } else {
    // assume it's attached to the Window object in a browser
    sax = this.sax;

    if (!sax) {
      // no sax for you!
      throw new Error(
        "Expected sax to be defined. Make sure you're including sax.js before this file.",
      );
    }
  }

  /**
   * XmlElement is our basic building block. Everything is an XmlElement; even XmlDocument
   * behaves like an XmlElement by inheriting its attributes and functions.
   */
  function XmlElement(tag, parser) {
    // If you didn't hand us a parser (common case) see if we can grab one
    // from the current execution stack.
    if (!parser) {
      var delegate = delegates[delegates.length - 1];

      if (delegate.parser) {
        parser = delegate.parser;
      }
    }

    this.name = tag.name;
    this.attr = tag.attributes;
    this.val = "";
    this.children = [];
    this.firstChild = null;
    this.lastChild = null;

    // Assign parse information
    this.line = parser ? parser.line : null;
    this.column = parser ? parser.column : null;
    this.position = parser ? parser.position : null;
    this.startTagPosition = parser ? parser.startTagPosition : null;
  }

  // Private methods

  XmlElement.prototype._addChild = function (child) {
    // add to our children array
    this.children.push(child);

    // update first/last pointers
    if (!this.firstChild) this.firstChild = child;
    this.lastChild = child;
  };

  // SaxParser handlers

  XmlElement.prototype._opentag = function (tag) {
    var child = new XmlElement(tag);

    this._addChild(child);

    delegates.unshift(child);
  };

  XmlElement.prototype._closetag = function () {
    delegates.shift();
  };

  XmlElement.prototype._text = function (text) {
    if (typeof this.children === "undefined") return;

    this.val += text;

    this._addChild(new XmlTextNode(text));
  };

  XmlElement.prototype._cdata = function (cdata) {
    this.val += cdata;

    this._addChild(new XmlCDataNode(cdata));
  };

  XmlElement.prototype._comment = function (comment) {
    if (typeof this.children === "undefined") return;

    this._addChild(new XmlCommentNode(comment));
  };

  XmlElement.prototype._error = function (err) {
    throw err;
  };

  // Useful functions

  XmlElement.prototype.eachChild = function (iterator, context) {
    for (var i = 0, l = this.children.length; i < l; i++)
      if (this.children[i].type === "element")
        if (
          iterator.call(context, this.children[i], i, this.children) === false
        )
          return;
  };

  XmlElement.prototype.childNamed = function (name) {
    for (var i = 0, l = this.children.length; i < l; i++) {
      var child = this.children[i];
      if (child.name === name) return child;
    }
    return undefined;
  };

  XmlElement.prototype.childrenNamed = function (name) {
    var matches = [];

    for (var i = 0, l = this.children.length; i < l; i++)
      if (this.children[i].name === name) matches.push(this.children[i]);

    return matches;
  };

  XmlElement.prototype.childWithAttribute = function (name, value) {
    for (var i = 0, l = this.children.length; i < l; i++) {
      var child = this.children[i];
      if (
        child.type === "element" &&
        ((value && child.attr[name] === value) || (!value && child.attr[name]))
      )
        return child;
    }
    return undefined;
  };

  XmlElement.prototype.descendantsNamed = function (name) {
    var matches = [];

    for (var i = 0, l = this.children.length; i < l; i++) {
      var child = this.children[i];
      if (child.type === "element") {
        if (child.name === name) matches.push(child);
        matches = matches.concat(child.descendantsNamed(name));
      }
    }

    return matches;
  };

  XmlElement.prototype.descendantWithPath = function (path) {
    var descendant = this;
    var components = path.split(".");

    for (var i = 0, l = components.length; i < l; i++)
      if (descendant && descendant.type === "element")
        descendant = descendant.childNamed(components[i]);
      else return undefined;

    return descendant;
  };

  XmlElement.prototype.valueWithPath = function (path) {
    var components = path.split("@");
    var descendant = this.descendantWithPath(components[0]);
    if (descendant)
      return components.length > 1
        ? descendant.attr[components[1]]
        : descendant.val;
    else return undefined;
  };

  // String formatting (for debugging)

  XmlElement.prototype.toString = function (options) {
    return this.toStringWithIndent("", options);
  };

  XmlElement.prototype.toStringWithIndent = function (indent, options) {
    var s = indent + "<" + this.name;
    var linebreak = options && options.compressed ? "" : "\n";
    var preserveWhitespace = options && options.preserveWhitespace;

    for (var name in this.attr)
      if (Object.prototype.hasOwnProperty.call(this.attr, name))
        s += " " + name + '="' + escapeXML(this.attr[name]) + '"';

    if (this.children.length === 1 && this.children[0].type !== "element") {
      s += ">" + this.children[0].toString(options) + "</" + this.name + ">";
    } else if (this.children.length) {
      s += ">" + linebreak;

      var childIndent = indent + (options && options.compressed ? "" : "  ");

      for (var i = 0, l = this.children.length; i < l; i++) {
        s +=
          this.children[i].toStringWithIndent(childIndent, options) + linebreak;
      }

      s += indent + "</" + this.name + ">";
    } else if (options && options.html) {
      var whiteList = [
        "area",
        "base",
        "br",
        "col",
        "embed",
        "frame",
        "hr",
        "img",
        "input",
        "keygen",
        "link",
        "menuitem",
        "meta",
        "param",
        "source",
        "track",
        "wbr",
      ];
      if (whiteList.indexOf(this.name) !== -1) s += "/>";
      else s += "></" + this.name + ">";
    } else {
      s += "/>";
    }

    return s;
  };

  // Alternative XML nodes

  function XmlTextNode(text) {
    this.text = text;
  }

  XmlTextNode.prototype.toString = function (options) {
    return formatText(escapeXML(this.text), options);
  };

  XmlTextNode.prototype.toStringWithIndent = function (indent, options) {
    return indent + this.toString(options);
  };

  function XmlCDataNode(cdata) {
    this.cdata = cdata;
  }

  XmlCDataNode.prototype.toString = function (options) {
    return "<![CDATA[" + formatText(this.cdata, options) + "]]>";
  };

  XmlCDataNode.prototype.toStringWithIndent = function (indent, options) {
    return indent + this.toString(options);
  };

  function XmlCommentNode(comment) {
    this.comment = comment;
  }

  XmlCommentNode.prototype.toString = function (options) {
    return "<!--" + formatText(escapeXML(this.comment), options) + "-->";
  };

  XmlCommentNode.prototype.toStringWithIndent = function (indent, options) {
    return indent + this.toString(options);
  };

  // Node type tag

  XmlElement.prototype.type = "element";
  XmlTextNode.prototype.type = "text";
  XmlCDataNode.prototype.type = "cdata";
  XmlCommentNode.prototype.type = "comment";

  /**
   * XmlDocument is the class we expose to the user; it uses the sax parser to create a hierarchy
   * of XmlElements.
   */
  function XmlDocument(xml) {
    xml && (xml = xml.toString().trim());

    if (!xml) throw new Error("No XML to parse!");

    // Stores doctype (if defined)
    this.doctype = "";

    // Expose the parser to the other delegates while the parser is running
    this.parser = sax.parser(true); // strict
    addParserEvents(this.parser);

    // We'll use the file-scoped "delegates" var to remember what elements we're currently
    // parsing; they will push and pop off the stack as we get deeper into the XML hierarchy.
    // It's safe to use a global because JS is single-threaded.
    delegates = [this];

    try {
      this.parser.write(xml);
    } finally {
      // Remove the parser as it is no longer needed and should not be exposed to clients
      delete this.parser;
    }
  }

  // make XmlDocument inherit XmlElement's methods
  extend(XmlDocument.prototype, XmlElement.prototype);

  XmlDocument.prototype._opentag = function (tag) {
    if (typeof this.children === "undefined")
      // the first tag we encounter should be the root - we'll "become" the root XmlElement
      XmlElement.call(this, tag);
    // all other tags will be the root element's children
    else XmlElement.prototype._opentag.apply(this, arguments);
  };

  XmlDocument.prototype._doctype = function (doctype) {
    this.doctype += doctype;
  };

  // file-scoped global stack of delegates
  var delegates = null;

  /*
   * Helper functions
   */

  function addParserEvents(parser) {
    parser.onopentag = parser_opentag;
    parser.onclosetag = parser_closetag;
    parser.ontext = parser_text;
    parser.oncdata = parser_cdata;
    parser.oncomment = parser_comment;
    parser.ondoctype = parser_doctype;
    parser.onerror = parser_error;
  }

  // create these closures and cache them by keeping them file-scoped
  function parser_opentag() {
    delegates[0] && delegates[0]._opentag.apply(delegates[0], arguments);
  }
  function parser_closetag() {
    delegates[0] && delegates[0]._closetag.apply(delegates[0], arguments);
  }
  function parser_text() {
    delegates[0] && delegates[0]._text.apply(delegates[0], arguments);
  }
  function parser_cdata() {
    delegates[0] && delegates[0]._cdata.apply(delegates[0], arguments);
  }
  function parser_comment() {
    delegates[0] && delegates[0]._comment.apply(delegates[0], arguments);
  }
  function parser_doctype() {
    delegates[0] && delegates[0]._doctype.apply(delegates[0], arguments);
  }
  function parser_error() {
    delegates[0] && delegates[0]._error.apply(delegates[0], arguments);
  }

  // a relatively standard extend method
  function extend(destination, source) {
    for (var prop in source)
      if (source.hasOwnProperty(prop)) destination[prop] = source[prop];
  }

  // escapes XML entities like "<", "&", etc.
  function escapeXML(value) {
    return value
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/'/g, "&apos;")
      .replace(/"/g, "&quot;");
  }

  // formats some text for debugging given a few options
  function formatText(text, options) {
    var finalText = text;

    if (options && options.trimmed && text.length > 25) {
      finalText = finalText.substring(0, 25).trim() + "…";
    }

    if (!(options && options.preserveWhitespace)) {
      finalText = finalText.trim();
    }

    return finalText;
  }

  // Are we being used in a Node-like environment?
  if (
     true &&
    module.exports &&
    !__webpack_require__.g.xmldocAssumeBrowser
  ) {
    module.exports.XmlDocument = XmlDocument;
    module.exports.XmlElement = XmlElement;
    module.exports.XmlTextNode = XmlTextNode;
    module.exports.XmlCDataNode = XmlCDataNode;
    module.exports.XmlCommentNode = XmlCommentNode;
  } else {
    this.XmlDocument = XmlDocument;
    this.XmlElement = XmlElement;
    this.XmlTextNode = XmlTextNode;
    this.XmlCDataNode = XmlCDataNode;
    this.XmlCommentNode = XmlCommentNode;
  }
})();


/***/ }),

/***/ 7785:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


// Internet Explorer polyfills
if (typeof window !== 'undefined' && !window.Promise) {
	__webpack_require__(98168);
}
__webpack_require__(83043);

var fetchUrl = function (url, headers) {
	return new Promise(function (resolve, reject) {
		var xhr = new XMLHttpRequest();
		xhr.open('GET', url, true);
		for (var headerName in headers) {
			xhr.setRequestHeader(headerName, headers[headerName]);
		}
		xhr.responseType = 'arraybuffer';

		xhr.onreadystatechange = function () {
			if (xhr.readyState !== 4) {
				return;
			}

			var ok = xhr.status >= 200 && xhr.status < 300;
			if (!ok) {
				setTimeout(function () {
					reject(new TypeError('Failed to fetch (url: "' + url + '")'));
				}, 0);
			}
		};

		xhr.onload = function () {
			var ok = xhr.status >= 200 && xhr.status < 300;
			if (ok) {
				resolve(xhr.response);
			}
		};

		xhr.onerror = function () {
			setTimeout(function () {
				reject(new TypeError('Network request failed (url: "' + url + '")'));
			}, 0);
		};

		xhr.ontimeout = function () {
			setTimeout(function () {
				reject(new TypeError('Network request failed (url: "' + url + '")'));
			}, 0);
		};

		xhr.send();
	});
};

function URLBrowserResolver(fs) {
	this.fs = fs;
	this.resolving = {};
}

URLBrowserResolver.prototype.resolve = function (url, headers) {
	if (!this.resolving[url]) {
		var _this = this;
		this.resolving[url] = new Promise(function (resolve, reject) {
			if (url.toLowerCase().indexOf('https://') === 0 || url.toLowerCase().indexOf('http://') === 0) {
				if (_this.fs.existsSync(url)) {
					// url was downloaded earlier
					resolve();
				} else {
					fetchUrl(url, headers).then(function (buffer) {
						_this.fs.writeFileSync(url, buffer);
						resolve();
					}, function (result) {
						reject(result);
					});
				}
			} else {
				// cannot be resolved
				resolve();
			}
		});
	}

	return this.resolving[url];
};

URLBrowserResolver.prototype.resolved = function () {
	var _this = this;
	return new Promise(function (resolve, reject) {
		Promise.all(Object.values(_this.resolving)).then(function () {
			resolve();
		}, function (result) {
			reject(result);
		});
	});
};

module.exports = URLBrowserResolver;


/***/ }),

/***/ 45314:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


var isFunction = (__webpack_require__(91867).isFunction);
var isUndefined = (__webpack_require__(91867).isUndefined);
//var isNull = require('../helpers').isNull;
var pack = (__webpack_require__(91867).pack);
var FileSaver = __webpack_require__(3502);
var saveAs = FileSaver.saveAs;

var defaultClientFonts = {
	Roboto: {
		normal: 'Roboto-Regular.ttf',
		bold: 'Roboto-Medium.ttf',
		italics: 'Roboto-Italic.ttf',
		bolditalics: 'Roboto-MediumItalic.ttf'
	}
};

var globalVfs;
var globalFonts;
var globalTableLayouts;

function Document(docDefinition, tableLayouts, fonts, vfs) {
	this.docDefinition = docDefinition;
	this.tableLayouts = tableLayouts || null;
	this.fonts = fonts || defaultClientFonts;
	this.vfs = vfs;
}

function canCreatePdf() {
	// Ensure the browser provides the level of support needed
	try {
		var arr = new Uint8Array(1);
		var proto = { foo: function () { return 42; } };
		Object.setPrototypeOf(proto, Uint8Array.prototype);
		Object.setPrototypeOf(arr, proto);
		return arr.foo() === 42;
	} catch (e) {
		return false;
	}
}

Document.prototype._createDoc = function (options, cb) {
	var getExtendedUrl = function (url) {
		if (typeof url === 'object') {
			return { url: url.url, headers: url.headers };
		}

		return { url: url, headers: {} };
	};

	options = options || {};
	if (this.tableLayouts) {
		options.tableLayouts = this.tableLayouts;
	}

	var PdfPrinter = __webpack_require__(81566);

	var printer = new PdfPrinter(this.fonts);
	(__webpack_require__(48181).bindFS)(this.vfs); // bind virtual file system to file system

	if (!isFunction(cb)) {
		var doc = printer.createPdfKitDocument(this.docDefinition, options);

		return doc;
	}

	var URLBrowserResolver = __webpack_require__(7785);
	var urlResolver = new URLBrowserResolver(__webpack_require__(48181));

	for (var font in this.fonts) {
		if (this.fonts.hasOwnProperty(font)) {
			if (this.fonts[font].normal) {
				if (Array.isArray(this.fonts[font].normal)) { // TrueType Collection
					var url = getExtendedUrl(this.fonts[font].normal[0]);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].normal[0] = url.url;
				} else {
					var url = getExtendedUrl(this.fonts[font].normal);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].normal = url.url;
				}
			}
			if (this.fonts[font].bold) {
				if (Array.isArray(this.fonts[font].bold)) { // TrueType Collection
					var url = getExtendedUrl(this.fonts[font].bold[0]);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].bold[0] = url.url;
				} else {
					var url = getExtendedUrl(this.fonts[font].bold);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].bold = url.url;
				}
			}
			if (this.fonts[font].italics) {
				if (Array.isArray(this.fonts[font].italics)) { // TrueType Collection
					var url = getExtendedUrl(this.fonts[font].italics[0]);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].italics[0] = url.url;
				} else {
					var url = getExtendedUrl(this.fonts[font].italics);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].italics = url.url;
				}
			}
			if (this.fonts[font].bolditalics) {
				if (Array.isArray(this.fonts[font].bolditalics)) { // TrueType Collection
					var url = getExtendedUrl(this.fonts[font].bolditalics[0]);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].bolditalics[0] = url.url;
				} else {
					var url = getExtendedUrl(this.fonts[font].bolditalics);
					urlResolver.resolve(url.url, url.headers);
					this.fonts[font].bolditalics = url.url;
				}
			}
		}
	}

	if (this.docDefinition.images) {
		for (var image in this.docDefinition.images) {
			if (this.docDefinition.images.hasOwnProperty(image)) {
				var url = getExtendedUrl(this.docDefinition.images[image]);
				urlResolver.resolve(url.url, url.headers);
				this.docDefinition.images[image] = url.url;
			}
		}
	}

	var _this = this;

	urlResolver.resolved().then(function () {
		var doc = printer.createPdfKitDocument(_this.docDefinition, options);

		cb(doc);
	}, function (result) {
		throw result;
	});
};

Document.prototype._flushDoc = function (doc, callback) {
	var chunks = [];
	var result;

	doc.on('readable', function () {
		var chunk;
		while ((chunk = doc.read(9007199254740991)) !== null) {
			chunks.push(chunk);
		}
	});
	doc.on('end', function () {
		result = Buffer.concat(chunks);
		callback(result, doc._pdfMakePages);
	});
	doc.end();
};

Document.prototype._getPages = function (options, cb) {
	if (!cb) {
		throw '_getPages is an async method and needs a callback argument';
	}
	var _this = this;

	this._createDoc(options, function (doc) {
		_this._flushDoc(doc, function (ignoreBuffer, pages) {
			cb(pages);
		});
	});
};

Document.prototype._bufferToBlob = function (buffer) {
	var blob;
	try {
		blob = new Blob([buffer], { type: 'application/pdf' });
	} catch (e) {
		// Old browser which can't handle it without making it an byte array (ie10)
		if (e.name === 'InvalidStateError') {
			var byteArray = new Uint8Array(buffer);
			blob = new Blob([byteArray.buffer], { type: 'application/pdf' });
		}
	}

	if (!blob) {
		throw 'Could not generate blob';
	}

	return blob;
};

Document.prototype._openWindow = function () {
	// we have to open the window immediately and store the reference
	// otherwise popup blockers will stop us
	var win = window.open('', '_blank');
	if (win === null) {
		throw 'Open PDF in new window blocked by browser';
	}

	return win;
};

Document.prototype._openPdf = function (options, win) {
	if (!win) {
		win = this._openWindow();
	}
	try {
		this.getBlob(function (result) {
			var urlCreator = window.URL || window.webkitURL;
			var pdfUrl = urlCreator.createObjectURL(result);
			win.location.href = pdfUrl;

			/* temporarily disabled
			if (win !== window) {
				setTimeout(function () {
					if (isNull(win.window)) { // is closed by AdBlock
						window.location.href = pdfUrl; // open in actual window
					}
				}, 500);
			}
			*/
		}, options);
	} catch (e) {
		win.close();
		throw e;
	}
};

Document.prototype.open = function (options, win) {
	options = options || {};
	options.autoPrint = false;
	win = win || null;

	this._openPdf(options, win);
};


Document.prototype.print = function (options, win) {
	options = options || {};
	options.autoPrint = true;
	win = win || null;

	this._openPdf(options, win);
};

/**
 * download(defaultFileName = 'file.pdf', cb = null, options = {})
 * or
 * download(cb, options = {})
 */
Document.prototype.download = function (defaultFileName, cb, options) {
	if (isFunction(defaultFileName)) {
		if (!isUndefined(cb)) {
			options = cb;
		}
		cb = defaultFileName;
		defaultFileName = null;
	}

	defaultFileName = defaultFileName || 'file.pdf';
	this.getBlob(function (result) {
		saveAs(result, defaultFileName);

		if (isFunction(cb)) {
			cb();
		}
	}, options);
};

Document.prototype.getBase64 = function (cb, options) {
	if (!cb) {
		throw 'getBase64 is an async method and needs a callback argument';
	}
	this.getBuffer(function (buffer) {
		cb(buffer.toString('base64'));
	}, options);
};

Document.prototype.getDataUrl = function (cb, options) {
	if (!cb) {
		throw 'getDataUrl is an async method and needs a callback argument';
	}
	this.getBuffer(function (buffer) {
		cb('data:application/pdf;base64,' + buffer.toString('base64'));
	}, options);
};

Document.prototype.getBlob = function (cb, options) {
	if (!cb) {
		throw 'getBlob is an async method and needs a callback argument';
	}
	var that = this;
	this.getBuffer(function (result) {
		var blob = that._bufferToBlob(result);
		cb(blob);
	}, options);
};

Document.prototype.getBuffer = function (cb, options) {
	if (!cb) {
		throw 'getBuffer is an async method and needs a callback argument';
	}

	var _this = this;

	this._createDoc(options, function (doc) {
		_this._flushDoc(doc, function (buffer) {
			cb(buffer);
		});
	});
};

Document.prototype.getStream = function (options, cb) {
	if (!isFunction(cb)) {
		var doc = this._createDoc(options);
		return doc;
	}

	this._createDoc(options, function (doc) {
		cb(doc);
	});
};

module.exports = {
	createPdf: function (docDefinition, tableLayouts, fonts, vfs) {
		if (!canCreatePdf()) {
			throw 'Your browser does not provide the level of support needed';
		}
		return new Document(
			docDefinition,
			tableLayouts || globalTableLayouts || __webpack_require__.g.pdfMake.tableLayouts,
			fonts || globalFonts || __webpack_require__.g.pdfMake.fonts,
			vfs || globalVfs || __webpack_require__.g.pdfMake.vfs
		);
	},
	addVirtualFileSystem: function (vfs) {
		globalVfs = vfs;
	},
	addFonts: function (fonts) {
		globalFonts = pack(globalFonts, fonts);
	},
	setFonts: function (fonts) {
		globalFonts = fonts;
	},
	clearFonts: function () {
		globalFonts = undefined;
	},
	addTableLayouts: function (tableLayouts) {
		globalTableLayouts = pack(globalTableLayouts, tableLayouts);
	},
	setTableLayouts: function (tableLayouts) {
		globalTableLayouts = tableLayouts;
	},
	clearTableLayouts: function () {
		globalTableLayouts = undefined;
	}
};


/***/ }),

/***/ 48181:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
var __dirname = "/";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


function VirtualFileSystem() {
	this.fileSystem = {};
	this.dataSystem = {};
}

VirtualFileSystem.prototype.existsSync = function (filename) {
	filename = fixFilename(filename);
	return typeof this.fileSystem[filename] !== 'undefined'
		|| typeof this.dataSystem[filename] !== 'undefined';
};

VirtualFileSystem.prototype.readFileSync = function (filename, options) {
	filename = fixFilename(filename);

	var dataContent = this.dataSystem[filename];
	if (typeof dataContent === 'string' && options === 'utf8') {
		return dataContent;
	}

	if (dataContent) {
		return new Buffer(dataContent, typeof dataContent === 'string' ? 'base64' : undefined);
	}

	var content = this.fileSystem[filename];
	if (content) {
		return content;
	}

	throw 'File \'' + filename + '\' not found in virtual file system';
};

VirtualFileSystem.prototype.writeFileSync = function (filename, content) {
	this.fileSystem[fixFilename(filename)] = content;
};

VirtualFileSystem.prototype.bindFS = function (data) {
	this.dataSystem = data || {};
};


function fixFilename(filename) {
	if (filename.indexOf(__dirname) === 0) {
		filename = filename.substring(__dirname.length);
	}

	if (filename.indexOf('/') === 0) {
		filename = filename.substring(1);
	}

	return filename;
}

module.exports = new VirtualFileSystem();


/***/ }),

/***/ 77530:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isString = (__webpack_require__(91867).isString);

function buildColumnWidths(columns, availableWidth, offsetTotal = 0, tableNode) {
	var autoColumns = [],
		autoMin = 0, autoMax = 0,
		starColumns = [],
		starMaxMin = 0,
		starMaxMax = 0,
		fixedColumns = [],
		initial_availableWidth = availableWidth;

	columns.forEach(function (column) {
		if (isAutoColumn(column)) {
			autoColumns.push(column);
			autoMin += column._minWidth;
			autoMax += column._maxWidth;
		} else if (isStarColumn(column)) {
			starColumns.push(column);
			starMaxMin = Math.max(starMaxMin, column._minWidth);
			starMaxMax = Math.max(starMaxMax, column._maxWidth);
		} else {
			fixedColumns.push(column);
		}
	});

	fixedColumns.forEach(function (col, colIndex) {
		// width specified as %
		if (isString(col.width) && /\d+%/.test(col.width)) {
			// In tables we have to take into consideration the reserved width for paddings and borders
			var reservedWidth = 0;
			if (tableNode) {
				var paddingLeft = tableNode._layout.paddingLeft(colIndex, tableNode);
				var paddingRight = tableNode._layout.paddingRight(colIndex, tableNode);
				var borderLeft = tableNode._layout.vLineWidth(colIndex, tableNode);
				var borderRight = tableNode._layout.vLineWidth(colIndex + 1, tableNode);
				if (colIndex === 0) {
					// first column assumes whole borderLeft and half of border right
					reservedWidth = paddingLeft + paddingRight + borderLeft + (borderRight / 2);

				} else if (colIndex === fixedColumns.length - 1) {
					// last column assumes whole borderRight and half of border left
					reservedWidth = paddingLeft + paddingRight + (borderLeft / 2) + borderRight;

				} else {
					// Columns in the middle assume half of each border
					reservedWidth = paddingLeft + paddingRight + (borderLeft / 2) + (borderRight / 2);
				}
			}
			var totalAvailableWidth = initial_availableWidth + offsetTotal;
			col.width = (parseFloat(col.width) * totalAvailableWidth / 100) - reservedWidth;
		}
		if (col.width < (col._minWidth) && col.elasticWidth) {
			col._calcWidth = col._minWidth;
		} else {
			col._calcWidth = col.width;
		}

		availableWidth -= col._calcWidth;
	});

	// http://www.freesoft.org/CIE/RFC/1942/18.htm
	// http://www.w3.org/TR/CSS2/tables.html#width-layout
	// http://dev.w3.org/csswg/css3-tables-algorithms/Overview.src.htm
	var minW = autoMin + starMaxMin * starColumns.length;
	var maxW = autoMax + starMaxMax * starColumns.length;
	if (minW >= availableWidth) {
		// case 1 - there's no way to fit all columns within available width
		// that's actually pretty bad situation with PDF as we have no horizontal scroll
		// no easy workaround (unless we decide, in the future, to split single words)
		// currently we simply use minWidths for all columns
		autoColumns.forEach(function (col) {
			col._calcWidth = col._minWidth;
		});

		starColumns.forEach(function (col) {
			col._calcWidth = starMaxMin; // starMaxMin already contains padding
		});
	} else {
		if (maxW < availableWidth) {
			// case 2 - we can fit rest of the table within available space
			autoColumns.forEach(function (col) {
				col._calcWidth = col._maxWidth;
				availableWidth -= col._calcWidth;
			});
		} else {
			// maxW is too large, but minW fits within available width
			var W = availableWidth - minW;
			var D = maxW - minW;

			autoColumns.forEach(function (col) {
				var d = col._maxWidth - col._minWidth;
				col._calcWidth = col._minWidth + d * W / D;
				availableWidth -= col._calcWidth;
			});
		}

		if (starColumns.length > 0) {
			var starSize = availableWidth / starColumns.length;

			starColumns.forEach(function (col) {
				col._calcWidth = starSize;
			});
		}
	}
}

function isAutoColumn(column) {
	return column.width === 'auto';
}

function isStarColumn(column) {
	return column.width === null || column.width === undefined || column.width === '*' || column.width === 'star';
}

//TODO: refactor and reuse in measureTable
function measureMinMax(columns) {
	var result = { min: 0, max: 0 };

	var maxStar = { min: 0, max: 0 };
	var starCount = 0;

	for (var i = 0, l = columns.length; i < l; i++) {
		var c = columns[i];

		if (isStarColumn(c)) {
			maxStar.min = Math.max(maxStar.min, c._minWidth);
			maxStar.max = Math.max(maxStar.max, c._maxWidth);
			starCount++;
		} else if (isAutoColumn(c)) {
			result.min += c._minWidth;
			result.max += c._maxWidth;
		} else {
			result.min += ((c.width !== undefined && c.width) || c._minWidth);
			result.max += ((c.width !== undefined && c.width) || c._maxWidth);
		}
	}

	if (starCount) {
		result.min += starCount * maxStar.min;
		result.max += starCount * maxStar.max;
	}

	return result;
}

/**
 * Calculates column widths
 * @private
 */
module.exports = {
	buildColumnWidths: buildColumnWidths,
	measureMinMax: measureMinMax,
	isAutoColumn: isAutoColumn,
	isStarColumn: isStarColumn
};


/***/ }),

/***/ 42526:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/



var TextTools = __webpack_require__(11548);
var StyleContextStack = __webpack_require__(76442);
var ColumnCalculator = __webpack_require__(77530);
var isString = (__webpack_require__(91867).isString);
var isNumber = (__webpack_require__(91867).isNumber);
var isObject = (__webpack_require__(91867).isObject);
var isArray = (__webpack_require__(91867).isArray);
var fontStringify = (__webpack_require__(91867).fontStringify);
var getNodeId = (__webpack_require__(91867).getNodeId);
var pack = (__webpack_require__(91867).pack);
var qrEncoder = __webpack_require__(72155);

/**
 * @private
 */
function DocMeasure(fontProvider, styleDictionary, defaultStyle, imageMeasure, svgMeasure, tableLayouts, images) {
	this.textTools = new TextTools(fontProvider);
	this.styleStack = new StyleContextStack(styleDictionary, defaultStyle);
	this.imageMeasure = imageMeasure;
	this.svgMeasure = svgMeasure;
	this.tableLayouts = tableLayouts;
	this.images = images;
	this.autoImageIndex = 1;
}

/**
 * Measures all nodes and sets min/max-width properties required for the second
 * layout-pass.
 * @param  {Object} docStructure document-definition-object
 * @return {Object}              document-measurement-object
 */
DocMeasure.prototype.measureDocument = function (docStructure) {
	return this.measureNode(docStructure);
};

DocMeasure.prototype.measureNode = function (node) {

	var self = this;

	return this.styleStack.auto(node, function () {
		// TODO: refactor + rethink whether this is the proper way to handle margins
		node._margin = getNodeMargin(node);

		if (node.columns) {
			return extendMargins(self.measureColumns(node));
		} else if (node.stack) {
			return extendMargins(self.measureVerticalContainer(node));
		} else if (node.ul) {
			return extendMargins(self.measureUnorderedList(node));
		} else if (node.ol) {
			return extendMargins(self.measureOrderedList(node));
		} else if (node.table) {
			return extendMargins(self.measureTable(node));
		} else if (node.text !== undefined) {
			return extendMargins(self.measureLeaf(node));
		} else if (node.toc) {
			return extendMargins(self.measureToc(node));
		} else if (node.image) {
			return extendMargins(self.measureImage(node));
		} else if (node.svg) {
			return extendMargins(self.measureSVG(node));
		} else if (node.canvas) {
			return extendMargins(self.measureCanvas(node));
		} else if (node.qr) {
			return extendMargins(self.measureQr(node));
		} else {
			throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
		}
	});

	function extendMargins(node) {
		var margin = node._margin;

		if (margin) {
			node._minWidth += margin[0] + margin[2];
			node._maxWidth += margin[0] + margin[2];
		}

		return node;
	}

	function getNodeMargin() {

		function processSingleMargins(node, currentMargin) {
			if (node.marginLeft || node.marginTop || node.marginRight || node.marginBottom) {
				return [
					node.marginLeft || currentMargin[0] || 0,
					node.marginTop || currentMargin[1] || 0,
					node.marginRight || currentMargin[2] || 0,
					node.marginBottom || currentMargin[3] || 0
				];
			}
			return currentMargin;
		}

		function flattenStyleArray(styleArray) {
			var flattenedStyles = {};
			for (var i = styleArray.length - 1; i >= 0; i--) {
				var styleName = styleArray[i];
				var style = self.styleStack.styleDictionary[styleName];
				for (var key in style) {
					if (style.hasOwnProperty(key)) {
						flattenedStyles[key] = style[key];
					}
				}
			}
			return flattenedStyles;
		}

		function convertMargin(margin) {
			if (isNumber(margin)) {
				margin = [margin, margin, margin, margin];
			} else if (isArray(margin)) {
				if (margin.length === 2) {
					margin = [margin[0], margin[1], margin[0], margin[1]];
				}
			}
			return margin;
		}

		var margin = [undefined, undefined, undefined, undefined];

		if (node.style) {
			var styleArray = isArray(node.style) ? node.style : [node.style];
			var flattenedStyleArray = flattenStyleArray(styleArray);

			if (flattenedStyleArray) {
				margin = processSingleMargins(flattenedStyleArray, margin);
			}

			if (flattenedStyleArray.margin) {
				margin = convertMargin(flattenedStyleArray.margin);
			}
		}

		margin = processSingleMargins(node, margin);

		if (node.margin) {
			margin = convertMargin(node.margin);
		}

		if (margin[0] === undefined && margin[1] === undefined && margin[2] === undefined && margin[3] === undefined) {
			return null;
		} else {
			return margin;
		}
	}
};

DocMeasure.prototype.convertIfBase64Image = function (node) {
	if (/^data:image\/(jpeg|jpg|png);base64,/.test(node.image)) {
		var label = '$$pdfmake$$' + this.autoImageIndex++;
		this.images[label] = node.image;
		node.image = label;
	}
};

DocMeasure.prototype.measureImageWithDimensions = function (node, dimensions) {
	if (node.fit) {
		var factor = (dimensions.width / dimensions.height > node.fit[0] / node.fit[1]) ? node.fit[0] / dimensions.width : node.fit[1] / dimensions.height;
		node._width = node._minWidth = node._maxWidth = dimensions.width * factor;
		node._height = dimensions.height * factor;
	} else if (node.cover) {
		node._width = node._minWidth = node._maxWidth = node.cover.width;
		node._height = node._minHeight = node._maxHeight = node.cover.height;
	} else {
		node._width = node._minWidth = node._maxWidth = node.width || dimensions.width;
		node._height = node.height || (dimensions.height * node._width / dimensions.width);

		if (isNumber(node.maxWidth) && node.maxWidth < node._width) {
			node._width = node._minWidth = node._maxWidth = node.maxWidth;
			node._height = node._width * dimensions.height / dimensions.width;
		}

		if (isNumber(node.maxHeight) && node.maxHeight < node._height) {
			node._height = node.maxHeight;
			node._width = node._minWidth = node._maxWidth = node._height * dimensions.width / dimensions.height;
		}

		if (isNumber(node.minWidth) && node.minWidth > node._width) {
			node._width = node._minWidth = node._maxWidth = node.minWidth;
			node._height = node._width * dimensions.height / dimensions.width;
		}

		if (isNumber(node.minHeight) && node.minHeight > node._height) {
			node._height = node.minHeight;
			node._width = node._minWidth = node._maxWidth = node._height * dimensions.width / dimensions.height;
		}
	}

	node._alignment = this.styleStack.getProperty('alignment');
};

DocMeasure.prototype.measureImage = function (node) {
	if (this.images) {
		this.convertIfBase64Image(node);
	}

	var dimensions = this.imageMeasure.measureImage(node.image);

	this.measureImageWithDimensions(node, dimensions);

	return node;
};

DocMeasure.prototype.measureSVG = function (node) {

	var dimensions = this.svgMeasure.measureSVG(node.svg);

	this.measureImageWithDimensions(node, dimensions);

	node.font = this.styleStack.getProperty('font');

	// scale SVG based on final dimension
	node.svg = this.svgMeasure.writeDimensions(node.svg, {
		width: node._width,
		height: node._height
	});

	return node;
};

DocMeasure.prototype.measureLeaf = function (node) {

	if (node._textRef && node._textRef._textNodeRef.text) {
		node.text = node._textRef._textNodeRef.text;
	}

	// Make sure style properties of the node itself are considered when building inlines.
	// We could also just pass [node] to buildInlines, but that fails for bullet points.
	var styleStack = this.styleStack.clone();
	styleStack.push(node);

	var data = this.textTools.buildInlines(node.text, styleStack);

	node._inlines = data.items;
	node._minWidth = data.minWidth;
	node._maxWidth = data.maxWidth;

	return node;
};

DocMeasure.prototype.measureToc = function (node) {
	if (node.toc.title) {
		node.toc.title = this.measureNode(node.toc.title);
	}

	if (node.toc._items.length > 0) {
		var body = [];
		var textStyle = node.toc.textStyle || {};
		var numberStyle = node.toc.numberStyle || textStyle;
		var textMargin = node.toc.textMargin || [0, 0, 0, 0];
		for (var i = 0, l = node.toc._items.length; i < l; i++) {
			var item = node.toc._items[i];
			var lineStyle = item._textNodeRef.tocStyle || textStyle;
			var lineMargin = item._textNodeRef.tocMargin || textMargin;
			var lineNumberStyle = item._textNodeRef.tocNumberStyle || numberStyle;
			var destination = getNodeId(item._nodeRef);
			body.push([
				{ text: item._textNodeRef.text, linkToDestination: destination, alignment: 'left', style: lineStyle, margin: lineMargin },
				{ text: '00000', linkToDestination: destination, alignment: 'right', _tocItemRef: item._nodeRef, style: lineNumberStyle, margin: [0, lineMargin[1], 0, lineMargin[3]] }
			]);
		}


		node.toc._table = {
			table: {
				dontBreakRows: true,
				widths: ['*', 'auto'],
				body: body
			},
			layout: 'noBorders'
		};

		node.toc._table = this.measureNode(node.toc._table);
	}

	return node;
};

DocMeasure.prototype.measureVerticalContainer = function (node) {
	var items = node.stack;

	node._minWidth = 0;
	node._maxWidth = 0;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.measureNode(items[i]);

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth);
	}

	return node;
};

DocMeasure.prototype.gapSizeForList = function () {
	return this.textTools.sizeOfString('9. ', this.styleStack);
};

DocMeasure.prototype.buildUnorderedMarker = function (styleStack, gapSize, type) {
	function buildDisc(gapSize, color) {
		// TODO: ascender-based calculations
		var radius = gapSize.fontSize / 6;
		return {
			canvas: [{
				x: radius,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - gapSize.fontSize / 3,
				r1: radius,
				r2: radius,
				type: 'ellipse',
				color: color
			}]
		};
	}

	function buildSquare(gapSize, color) {
		// TODO: ascender-based calculations
		var size = gapSize.fontSize / 3;
		return {
			canvas: [{
				x: 0,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - (gapSize.fontSize / 3) - (size / 2),
				h: size,
				w: size,
				type: 'rect',
				color: color
			}]
		};
	}

	function buildCircle(gapSize, color) {
		// TODO: ascender-based calculations
		var radius = gapSize.fontSize / 6;
		return {
			canvas: [{
				x: radius,
				y: (gapSize.height / gapSize.lineHeight) + gapSize.descender - gapSize.fontSize / 3,
				r1: radius,
				r2: radius,
				type: 'ellipse',
				lineColor: color
			}]
		};
	}

	var marker;
	var color = styleStack.getProperty('markerColor') || styleStack.getProperty('color') || 'black';

	switch (type) {
		case 'circle':
			marker = buildCircle(gapSize, color);
			break;

		case 'square':
			marker = buildSquare(gapSize, color);
			break;

		case 'none':
			marker = {};
			break;

		case 'disc':
		default:
			marker = buildDisc(gapSize, color);
			break;
	}

	marker._minWidth = marker._maxWidth = gapSize.width;
	marker._minHeight = marker._maxHeight = gapSize.height;

	return marker;
};

DocMeasure.prototype.buildOrderedMarker = function (counter, styleStack, type, separator) {
	function prepareAlpha(counter) {
		function toAlpha(num) {
			return (num >= 26 ? toAlpha((num / 26 >> 0) - 1) : '') + 'abcdefghijklmnopqrstuvwxyz'[num % 26 >> 0];
		}

		if (counter < 1) {
			return counter.toString();
		}

		return toAlpha(counter - 1);
	}

	function prepareRoman(counter) {
		if (counter < 1 || counter > 4999) {
			return counter.toString();
		}
		var num = counter;
		var lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 }, roman = '', i;
		for (i in lookup) {
			while (num >= lookup[i]) {
				roman += i;
				num -= lookup[i];
			}
		}
		return roman;
	}

	function prepareDecimal(counter) {
		return counter.toString();
	}

	var counterText;
	switch (type) {
		case 'none':
			counterText = null;
			break;

		case 'upper-alpha':
			counterText = prepareAlpha(counter).toUpperCase();
			break;

		case 'lower-alpha':
			counterText = prepareAlpha(counter);
			break;

		case 'upper-roman':
			counterText = prepareRoman(counter);
			break;

		case 'lower-roman':
			counterText = prepareRoman(counter).toLowerCase();
			break;

		case 'decimal':
		default:
			counterText = prepareDecimal(counter);
			break;
	}

	if (counterText === null) {
		return {};
	}

	if (separator) {
		if (isArray(separator)) {
			if (separator[0]) {
				counterText = separator[0] + counterText;
			}

			if (separator[1]) {
				counterText += separator[1];
			}
			counterText += ' ';
		} else {
			counterText += separator + ' ';
		}
	}

	var textArray = { text: counterText };
	var markerColor = styleStack.getProperty('markerColor');
	if (markerColor) {
		textArray.color = markerColor;
	}

	return { _inlines: this.textTools.buildInlines(textArray, styleStack).items };
};

DocMeasure.prototype.measureUnorderedList = function (node) {
	var style = this.styleStack.clone();
	var items = node.ul;
	node.type = node.type || 'disc';
	node._gapSize = this.gapSizeForList();
	node._minWidth = 0;
	node._maxWidth = 0;

	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i] = this.measureNode(items[i]);

		if (!item.ol && !item.ul) {
			item.listMarker = this.buildUnorderedMarker(style, node._gapSize, item.listType || node.type);
		}

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth + node._gapSize.width);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth + node._gapSize.width);
	}

	return node;
};

DocMeasure.prototype.measureOrderedList = function (node) {
	var style = this.styleStack.clone();
	var items = node.ol;
	node.type = node.type || 'decimal';
	node.separator = node.separator || '.';
	node.reversed = node.reversed || false;
	if (!isNumber(node.start)) {
		node.start = node.reversed ? items.length : 1;
	}
	node._gapSize = this.gapSizeForList();
	node._minWidth = 0;
	node._maxWidth = 0;

	var counter = node.start;
	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i] = this.measureNode(items[i]);

		if (!item.ol && !item.ul) {
			var counterValue = isNumber(item.counter) ? item.counter : counter;
			item.listMarker = this.buildOrderedMarker(counterValue, style, item.listType || node.type, node.separator);
			if (item.listMarker._inlines) {
				node._gapSize.width = Math.max(node._gapSize.width, item.listMarker._inlines[0].width);
			}

			if (node.reversed) {
				counter--;
			} else {
				counter++;
			}
		}

		node._minWidth = Math.max(node._minWidth, items[i]._minWidth);
		node._maxWidth = Math.max(node._maxWidth, items[i]._maxWidth);
	}

	node._minWidth += node._gapSize.width;
	node._maxWidth += node._gapSize.width;

	for (var i = 0, l = items.length; i < l; i++) {
		var item = items[i];
		if (!item.ol && !item.ul) {
			item.listMarker._minWidth = item.listMarker._maxWidth = node._gapSize.width;
		}
	}

	return node;
};

DocMeasure.prototype.measureColumns = function (node) {
	var columns = node.columns;
	node._gap = this.styleStack.getProperty('columnGap') || 0;

	for (var i = 0, l = columns.length; i < l; i++) {
		columns[i] = this.measureNode(columns[i]);
	}

	var measures = ColumnCalculator.measureMinMax(columns);

	var numGaps = (columns.length > 0) ? (columns.length - 1) : 0;
	node._minWidth = measures.min + node._gap * numGaps;
	node._maxWidth = measures.max + node._gap * numGaps;

	return node;
};

DocMeasure.prototype.measureTable = function (node) {
	extendTableWidths(node);
	node._layout = getLayout(this.tableLayouts);
	node._offsets = getOffsets(node._layout);

	var colSpans = [];
	var col, row, cols, rows;

	for (col = 0, cols = node.table.body[0].length; col < cols; col++) {
		var c = node.table.widths[col];
		c._minWidth = 0;
		c._maxWidth = 0;

		for (row = 0, rows = node.table.body.length; row < rows; row++) {
			var rowData = node.table.body[row];
			var data = rowData[col];
			if (data === undefined) {
				console.error('Malformed table row ', rowData, 'in node ', node);
				throw 'Malformed table row, a cell is undefined.';
			}
			if (data === null) { // transform to object
				data = '';
			}

			if (!data._span) {
				data = rowData[col] = this.styleStack.auto(data, measureCb(this, data));

				if (data.colSpan && data.colSpan > 1) {
					markSpans(rowData, col, data.colSpan);
					colSpans.push({ col: col, span: data.colSpan, minWidth: data._minWidth, maxWidth: data._maxWidth });
				} else {
					c._minWidth = Math.max(c._minWidth, data._minWidth);
					c._maxWidth = Math.max(c._maxWidth, data._maxWidth);
				}
			}

			if (data.rowSpan && data.rowSpan > 1) {
				markVSpans(node.table, row, col, data.rowSpan);
			}
		}
	}

	extendWidthsForColSpans();

	var measures = ColumnCalculator.measureMinMax(node.table.widths);

	node._minWidth = measures.min + node._offsets.total;
	node._maxWidth = measures.max + node._offsets.total;

	return node;

	function measureCb(_this, data) {
		return function () {
			if (isObject(data)) {
				data.fillColor = _this.styleStack.getProperty('fillColor');
				data.fillOpacity = _this.styleStack.getProperty('fillOpacity');
			}
			return _this.measureNode(data);
		};
	}

	function getLayout(tableLayouts) {
		var layout = node.layout;

		if (isString(layout)) {
			layout = tableLayouts[layout];
		}

		var defaultLayout = {
			hLineWidth: function (i, node) {
				return 1;
			},
			vLineWidth: function (i, node) {
				return 1;
			},
			hLineColor: function (i, node) {
				return 'black';
			},
			vLineColor: function (i, node) {
				return 'black';
			},
			hLineStyle: function (i, node) {
				return null;
			},
			vLineStyle: function (i, node) {
				return null;
			},
			paddingLeft: function (i, node) {
				return 4;
			},
			paddingRight: function (i, node) {
				return 4;
			},
			paddingTop: function (i, node) {
				return 2;
			},
			paddingBottom: function (i, node) {
				return 2;
			},
			fillColor: function (i, node) {
				return null;
			},
			fillOpacity: function (i, node) {
				return 1;
			},
			defaultBorder: true
		};

		return pack(defaultLayout, layout);
	}

	function getOffsets(layout) {
		var offsets = [];
		var totalOffset = 0;
		var prevRightPadding = 0;

		for (var i = 0, l = node.table.widths.length; i < l; i++) {
			var lOffset = prevRightPadding + layout.vLineWidth(i, node) + layout.paddingLeft(i, node);
			offsets.push(lOffset);
			totalOffset += lOffset;
			prevRightPadding = layout.paddingRight(i, node);
		}

		totalOffset += prevRightPadding + layout.vLineWidth(node.table.widths.length, node);

		return {
			total: totalOffset,
			offsets: offsets
		};
	}

	function extendWidthsForColSpans() {
		var q, j;

		for (var i = 0, l = colSpans.length; i < l; i++) {
			var span = colSpans[i];

			var currentMinMax = getMinMax(span.col, span.span, node._offsets);
			var minDifference = span.minWidth - currentMinMax.minWidth;
			var maxDifference = span.maxWidth - currentMinMax.maxWidth;

			if (minDifference > 0) {
				q = minDifference / span.span;

				for (j = 0; j < span.span; j++) {
					node.table.widths[span.col + j]._minWidth += q;
				}
			}

			if (maxDifference > 0) {
				q = maxDifference / span.span;

				for (j = 0; j < span.span; j++) {
					node.table.widths[span.col + j]._maxWidth += q;
				}
			}
		}
	}

	function getMinMax(col, span, offsets) {
		var result = { minWidth: 0, maxWidth: 0 };

		for (var i = 0; i < span; i++) {
			result.minWidth += node.table.widths[col + i]._minWidth + (i ? offsets.offsets[col + i] : 0);
			result.maxWidth += node.table.widths[col + i]._maxWidth + (i ? offsets.offsets[col + i] : 0);
		}

		return result;
	}

	function markSpans(rowData, col, span) {
		for (var i = 1; i < span; i++) {
			rowData[col + i] = {
				_span: true,
				_minWidth: 0,
				_maxWidth: 0,
				rowSpan: rowData[col].rowSpan
			};
		}
	}

	function markVSpans(table, row, col, span) {
		for (var i = 1; i < span; i++) {
			table.body[row + i][col] = {
				_span: true,
				_minWidth: 0,
				_maxWidth: 0,
				fillColor: table.body[row][col].fillColor,
				fillOpacity: table.body[row][col].fillOpacity
			};
		}
	}

	function extendTableWidths(node) {
		if (!node.table.widths) {
			node.table.widths = 'auto';
		}

		if (isString(node.table.widths)) {
			node.table.widths = [node.table.widths];

			while (node.table.widths.length < node.table.body[0].length) {
				node.table.widths.push(node.table.widths[node.table.widths.length - 1]);
			}
		}

		for (var i = 0, l = node.table.widths.length; i < l; i++) {
			var w = node.table.widths[i];
			if (isNumber(w) || isString(w)) {
				node.table.widths[i] = { width: w };
			}
		}
	}
};

DocMeasure.prototype.measureCanvas = function (node) {
	var w = 0, h = 0;

	for (var i = 0, l = node.canvas.length; i < l; i++) {
		var vector = node.canvas[i];

		switch (vector.type) {
			case 'ellipse':
				w = Math.max(w, vector.x + vector.r1);
				h = Math.max(h, vector.y + vector.r2);
				break;
			case 'rect':
				w = Math.max(w, vector.x + vector.w);
				h = Math.max(h, vector.y + vector.h);
				break;
			case 'line':
				w = Math.max(w, vector.x1, vector.x2);
				h = Math.max(h, vector.y1, vector.y2);
				break;
			case 'polyline':
				for (var i2 = 0, l2 = vector.points.length; i2 < l2; i2++) {
					w = Math.max(w, vector.points[i2].x);
					h = Math.max(h, vector.points[i2].y);
				}
				break;
		}
	}

	node._minWidth = node._maxWidth = w;
	node._minHeight = node._maxHeight = h;
	node._alignment = this.styleStack.getProperty('alignment');

	return node;
};

DocMeasure.prototype.measureQr = function (node) {
	node = qrEncoder.measure(node);
	node._alignment = this.styleStack.getProperty('alignment');
	return node;
};

module.exports = DocMeasure;


/***/ }),

/***/ 98883:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


var isString = (__webpack_require__(91867).isString);
var isNumber = (__webpack_require__(91867).isNumber);
var isBoolean = (__webpack_require__(91867).isBoolean);
var isArray = (__webpack_require__(91867).isArray);
var isUndefined = (__webpack_require__(91867).isUndefined);
var fontStringify = (__webpack_require__(91867).fontStringify);

function DocPreprocessor() {

}

DocPreprocessor.prototype.preprocessDocument = function (docStructure) {
	this.parentNode = null;
	this.tocs = [];
	this.nodeReferences = [];
	return this.preprocessNode(docStructure);
};

DocPreprocessor.prototype.preprocessNode = function (node) {
	// expand shortcuts and casting values
	if (isArray(node)) {
		node = { stack: node };
	} else if (isString(node)) {
		node = { text: node };
	} else if (isNumber(node) || isBoolean(node)) {
		node = { text: node.toString() };
	} else if (node === undefined || node === null) {
		node = { text: '' };
	} else if (Object.keys(node).length === 0) { // empty object
		node = { text: '' };
	} else if ('text' in node && (node.text === undefined || node.text === null)) {
		node.text = '';
	}

	if (node.columns) {
		return this.preprocessColumns(node);
	} else if (node.stack) {
		return this.preprocessVerticalContainer(node);
	} else if (node.ul) {
		return this.preprocessList(node);
	} else if (node.ol) {
		return this.preprocessList(node);
	} else if (node.table) {
		return this.preprocessTable(node);
	} else if (node.text !== undefined) {
		return this.preprocessText(node);
	} else if (node.toc) {
		return this.preprocessToc(node);
	} else if (node.image) {
		return this.preprocessImage(node);
	} else if (node.svg) {
		return this.preprocessSVG(node);
	} else if (node.canvas) {
		return this.preprocessCanvas(node);
	} else if (node.qr) {
		return this.preprocessQr(node);
	} else if (node.pageReference || node.textReference) {
		return this.preprocessText(node);
	} else {
		throw 'Unrecognized document structure: ' + JSON.stringify(node, fontStringify);
	}
};

DocPreprocessor.prototype.preprocessColumns = function (node) {
	var columns = node.columns;

	for (var i = 0, l = columns.length; i < l; i++) {
		columns[i] = this.preprocessNode(columns[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessVerticalContainer = function (node) {
	var items = node.stack;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.preprocessNode(items[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessList = function (node) {
	var items = node.ul || node.ol;

	for (var i = 0, l = items.length; i < l; i++) {
		items[i] = this.preprocessNode(items[i]);
	}

	return node;
};

DocPreprocessor.prototype.preprocessTable = function (node) {
	var col, row, cols, rows;

	for (col = 0, cols = node.table.body[0].length; col < cols; col++) {
		for (row = 0, rows = node.table.body.length; row < rows; row++) {
			var rowData = node.table.body[row];
			var data = rowData[col];
			if (data !== undefined) {
				if (data === null) { // transform to object
					data = '';
				}
				if (!data._span) {
					rowData[col] = this.preprocessNode(data);
				}
			}
		}
	}

	return node;
};

DocPreprocessor.prototype.preprocessText = function (node) {
	if (node.tocItem) {
		if (!isArray(node.tocItem)) {
			node.tocItem = [node.tocItem];
		}

		for (var i = 0, l = node.tocItem.length; i < l; i++) {
			if (!isString(node.tocItem[i])) {
				node.tocItem[i] = '_default_';
			}

			var tocItemId = node.tocItem[i];

			if (!this.tocs[tocItemId]) {
				this.tocs[tocItemId] = { toc: { _items: [], _pseudo: true } };
			}

			if (!node.id) {
				node.id = 'toc-' + tocItemId + '-' + this.tocs[tocItemId].toc._items.length;
			}

			var tocItemRef = {
				_nodeRef: this._getNodeForNodeRef(node),
				_textNodeRef: node
			};
			this.tocs[tocItemId].toc._items.push(tocItemRef);
		}
	}

	if (node.id) {
		if (this.nodeReferences[node.id]) {
			if (!this.nodeReferences[node.id]._pseudo) {
				throw "Node id '" + node.id + "' already exists";
			}

			this.nodeReferences[node.id]._nodeRef = this._getNodeForNodeRef(node);
			this.nodeReferences[node.id]._textNodeRef = node;
			this.nodeReferences[node.id]._pseudo = false;
		} else {
			this.nodeReferences[node.id] = {
				_nodeRef: this._getNodeForNodeRef(node),
				_textNodeRef: node
			};
		}
	}

	if (node.pageReference) {
		if (!this.nodeReferences[node.pageReference]) {
			this.nodeReferences[node.pageReference] = {
				_nodeRef: {},
				_textNodeRef: {},
				_pseudo: true
			};
		}
		node.text = '00000';
		node.linkToDestination = node.pageReference;
		node._pageRef = this.nodeReferences[node.pageReference];
	}

	if (node.textReference) {
		if (!this.nodeReferences[node.textReference]) {
			this.nodeReferences[node.textReference] = { _nodeRef: {}, _pseudo: true };
		}

		node.text = '';
		node.linkToDestination = node.textReference;
		node._textRef = this.nodeReferences[node.textReference];
	}

	if (node.text && node.text.text) {
		node.text = [this.preprocessNode(node.text)];
	} else if (isArray(node.text)) {
		var isSetParentNode = false;
		if (this.parentNode === null) {
			this.parentNode = node;
			isSetParentNode = true;
		}

		for (var i = 0, l = node.text.length; i < l; i++) {
			node.text[i] = this.preprocessNode(node.text[i]);
		}

		if (isSetParentNode) {
			this.parentNode = null;
		}
	}

	return node;
};

DocPreprocessor.prototype.preprocessToc = function (node) {
	if (!node.toc.id) {
		node.toc.id = '_default_';
	}

	node.toc.title = node.toc.title ? this.preprocessNode(node.toc.title) : null;
	node.toc._items = [];

	if (this.tocs[node.toc.id]) {
		if (!this.tocs[node.toc.id].toc._pseudo) {
			throw "TOC '" + node.toc.id + "' already exists";
		}

		node.toc._items = this.tocs[node.toc.id].toc._items;
	}

	this.tocs[node.toc.id] = node;

	return node;
};

DocPreprocessor.prototype.preprocessImage = function (node) {
	if (!isUndefined(node.image.type) && !isUndefined(node.image.data) && (node.image.type === 'Buffer') && isArray(node.image.data)) {
		node.image = Buffer.from(node.image.data);
	}
	return node;
};

DocPreprocessor.prototype.preprocessSVG = function (node) {
	return node;
};

DocPreprocessor.prototype.preprocessCanvas = function (node) {
	return node;
};

DocPreprocessor.prototype.preprocessQr = function (node) {
	return node;
};

DocPreprocessor.prototype._getNodeForNodeRef = function (node) {
	if (this.parentNode) {
		return this.parentNode;
	}

	return node;
};

module.exports = DocPreprocessor;


/***/ }),

/***/ 79178:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var TraversalTracker = __webpack_require__(2318);
var isString = (__webpack_require__(91867).isString);

/**
 * Creates an instance of DocumentContext - a store for current x, y positions and available width/height.
 * It facilitates column divisions and vertical sync
 */
function DocumentContext(pageSize, pageMargins) {
	this.pages = [];

	this.pageMargins = pageMargins;

	this.x = pageMargins.left;
	this.availableWidth = pageSize.width - pageMargins.left - pageMargins.right;
	this.availableHeight = 0;
	this.page = -1;

	this.snapshots = [];

	this.tracker = new TraversalTracker();

	this.backgroundLength = [];

	this.addPage(pageSize);
}

DocumentContext.prototype.beginColumnGroup = function (marginXTopParent, bottomByPage = {}) {
	this.snapshots.push({
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		page: this.page,
		bottomByPage: bottomByPage ? bottomByPage : {},
		bottomMost: {
			x: this.x,
			y: this.y,
			availableHeight: this.availableHeight,
			availableWidth: this.availableWidth,
			page: this.page
		},
		lastColumnWidth: this.lastColumnWidth
	});

	this.lastColumnWidth = 0;
	if (marginXTopParent) {
		this.marginXTopParent = marginXTopParent;
	}
};

DocumentContext.prototype.updateBottomByPage = function () {
	const lastSnapshot = this.snapshots[this.snapshots.length - 1];
	const lastPage = this.page;
	let previousBottom = -Number.MIN_VALUE;
	if (lastSnapshot.bottomByPage[lastPage]) {
		previousBottom = lastSnapshot.bottomByPage[lastPage];
	}
	lastSnapshot.bottomByPage[lastPage] = Math.max(previousBottom, this.y);
};

DocumentContext.prototype.resetMarginXTopParent = function () {
	this.marginXTopParent = null;
};

DocumentContext.prototype.beginColumn = function (width, offset, endingCell) {
	var saved = this.snapshots[this.snapshots.length - 1];

	this.calculateBottomMost(saved, endingCell);

	this.page = saved.page;
	this.x = this.x + this.lastColumnWidth + (offset || 0);
	this.y = saved.y;
	this.availableWidth = width;	//saved.availableWidth - offset;
	this.availableHeight = saved.availableHeight;

	this.lastColumnWidth = width;
};

DocumentContext.prototype.calculateBottomMost = function (destContext, endingCell) {
	if (endingCell) {
		this.saveContextInEndingCell(endingCell);
	} else {
		destContext.bottomMost = bottomMostContext(this, destContext.bottomMost);
	}
};

DocumentContext.prototype.markEnding = function (endingCell, originalXOffset, discountY) {
	this.page = endingCell._columnEndingContext.page;
	this.x = endingCell._columnEndingContext.x + originalXOffset;
	this.y = endingCell._columnEndingContext.y - discountY;
	this.availableWidth = endingCell._columnEndingContext.availableWidth;
	this.availableHeight = endingCell._columnEndingContext.availableHeight;
	this.lastColumnWidth = endingCell._columnEndingContext.lastColumnWidth;
};

DocumentContext.prototype.saveContextInEndingCell = function (endingCell) {
	endingCell._columnEndingContext = {
		page: this.page,
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		lastColumnWidth: this.lastColumnWidth
	};
};

DocumentContext.prototype.completeColumnGroup = function (height, endingCell) {
	var saved = this.snapshots.pop();

	this.calculateBottomMost(saved, endingCell);

	this.x = saved.x;

	var y = saved.bottomMost.y;
	if (height) {
		if (saved.page === saved.bottomMost.page) {
			if ((saved.y + height) > y) {
				y = saved.y + height;
			}
		} else {
			y += height;
		}
	}

	this.y = y;
	this.page = saved.bottomMost.page;
	this.availableWidth = saved.availableWidth;
	this.availableHeight = saved.bottomMost.availableHeight;
	if (height) {
		this.availableHeight -= (y - saved.bottomMost.y);
	}
	this.lastColumnWidth = saved.lastColumnWidth;
	return saved.bottomByPage;
};

DocumentContext.prototype.addMargin = function (left, right) {
	this.x += left;
	this.availableWidth -= left + (right || 0);
};

DocumentContext.prototype.moveDown = function (offset) {
	this.y += offset;
	this.availableHeight -= offset;

	return this.availableHeight > 0;
};

DocumentContext.prototype.initializePage = function () {
	this.y = this.pageMargins.top;
	this.availableHeight = this.getCurrentPage().pageSize.height - this.pageMargins.top - this.pageMargins.bottom;
	const { pageCtx, isSnapshot } = this.pageSnapshot();
	pageCtx.availableWidth = this.getCurrentPage().pageSize.width - this.pageMargins.left - this.pageMargins.right;
	if (isSnapshot && this.marginXTopParent) {
		pageCtx.availableWidth -= this.marginXTopParent[0];
		pageCtx.availableWidth -= this.marginXTopParent[1];
	}
};

DocumentContext.prototype.pageSnapshot = function () {
	if (this.snapshots[0]) {
		return { pageCtx: this.snapshots[0], isSnapshot: true };
	} else {
		return { pageCtx: this, isSnapshot: false };
	}
};

DocumentContext.prototype.moveTo = function (x, y) {
	if (x !== undefined && x !== null) {
		this.x = x;
		this.availableWidth = this.getCurrentPage().pageSize.width - this.x - this.pageMargins.right;
	}
	if (y !== undefined && y !== null) {
		this.y = y;
		this.availableHeight = this.getCurrentPage().pageSize.height - this.y - this.pageMargins.bottom;
	}
};

DocumentContext.prototype.moveToRelative = function (x, y) {
	if (x !== undefined && x !== null) {
		this.x = this.x + x;
	}
	if (y !== undefined && y !== null) {
		this.y = this.y + y;
	}
};

DocumentContext.prototype.beginDetachedBlock = function () {
	this.snapshots.push({
		x: this.x,
		y: this.y,
		availableHeight: this.availableHeight,
		availableWidth: this.availableWidth,
		page: this.page,
		lastColumnWidth: this.lastColumnWidth
	});
};

DocumentContext.prototype.endDetachedBlock = function () {
	var saved = this.snapshots.pop();

	this.x = saved.x;
	this.y = saved.y;
	this.availableWidth = saved.availableWidth;
	this.availableHeight = saved.availableHeight;
	this.page = saved.page;
	this.lastColumnWidth = saved.lastColumnWidth;
};

function pageOrientation(pageOrientationString, currentPageOrientation) {
	if (pageOrientationString === undefined) {
		return currentPageOrientation;
	} else if (isString(pageOrientationString) && (pageOrientationString.toLowerCase() === 'landscape')) {
		return 'landscape';
	} else {
		return 'portrait';
	}
}

var getPageSize = function (currentPage, newPageOrientation) {

	newPageOrientation = pageOrientation(newPageOrientation, currentPage.pageSize.orientation);

	if (newPageOrientation !== currentPage.pageSize.orientation) {
		return {
			orientation: newPageOrientation,
			width: currentPage.pageSize.height,
			height: currentPage.pageSize.width
		};
	} else {
		return {
			orientation: currentPage.pageSize.orientation,
			width: currentPage.pageSize.width,
			height: currentPage.pageSize.height
		};
	}

};


DocumentContext.prototype.moveToNextPage = function (pageOrientation) {
	var nextPageIndex = this.page + 1;

	var prevPage = this.page;
	var prevY = this.y;

	// If we are in a column group
	if (this.snapshots.length > 0) {
		var lastSnapshot = this.snapshots[this.snapshots.length - 1];
		// We have to update prevY accordingly by also taking into consideration
		// the 'y' of cells that don't break page
		if (lastSnapshot.bottomMost && lastSnapshot.bottomMost.y) {
			prevY = Math.max(this.y, lastSnapshot.bottomMost.y);
		}
	}

	var createNewPage = nextPageIndex >= this.pages.length;
	if (createNewPage) {
		var currentAvailableWidth = this.availableWidth;
		var currentPageOrientation = this.getCurrentPage().pageSize.orientation;

		var pageSize = getPageSize(this.getCurrentPage(), pageOrientation);
		this.addPage(pageSize);

		if (currentPageOrientation === pageSize.orientation) {
			this.availableWidth = currentAvailableWidth;
		}
	} else {
		this.page = nextPageIndex;
		this.initializePage();
	}

	return {
		newPageCreated: createNewPage,
		prevPage: prevPage,
		prevY: prevY,
		y: this.y
	};
};


DocumentContext.prototype.addPage = function (pageSize) {
	var page = { items: [], pageSize: pageSize };
	this.pages.push(page);
	this.backgroundLength.push(0);
	this.page = this.pages.length - 1;
	this.initializePage();

	this.tracker.emit('pageAdded');

	return page;
};

DocumentContext.prototype.getCurrentPage = function () {
	if (this.page < 0 || this.page >= this.pages.length) {
		return null;
	}

	return this.pages[this.page];
};

DocumentContext.prototype.getCurrentPosition = function () {
	var pageSize = this.getCurrentPage().pageSize;
	var innerHeight = pageSize.height - this.pageMargins.top - this.pageMargins.bottom;
	var innerWidth = pageSize.width - this.pageMargins.left - this.pageMargins.right;

	return {
		pageNumber: this.page + 1,
		pageOrientation: pageSize.orientation,
		pageInnerHeight: innerHeight,
		pageInnerWidth: innerWidth,
		left: this.x,
		top: this.y,
		verticalRatio: ((this.y - this.pageMargins.top) / innerHeight),
		horizontalRatio: ((this.x - this.pageMargins.left) / innerWidth)
	};
};

function bottomMostContext(c1, c2) {
	var r;

	if (c1.page > c2.page) {
		r = c1;
	} else if (c2.page > c1.page) {
		r = c2;
	} else {
		r = (c1.y > c2.y) ? c1 : c2;
	}

	return {
		page: r.page,
		x: r.x,
		y: r.y,
		availableHeight: r.availableHeight,
		availableWidth: r.availableWidth
	};
}

module.exports = DocumentContext;


/***/ }),

/***/ 54861:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var Line = __webpack_require__(70770);
var isNumber = (__webpack_require__(91867).isNumber);
var pack = (__webpack_require__(91867).pack);
var offsetVector = (__webpack_require__(91867).offsetVector);
var DocumentContext = __webpack_require__(79178);

/**
 * Creates an instance of ElementWriter - a line/vector writer, which adds
 * elements to current page and sets their positions based on the context
 */
function ElementWriter(context, tracker) {
	this.context = context;
	this.contextStack = [];
	this.tracker = tracker;
}

function addPageItem(page, item, index) {
	if (index === null || index === undefined || index < 0 || index > page.items.length) {
		page.items.push(item);
	} else {
		page.items.splice(index, 0, item);
	}
}

ElementWriter.prototype.addLine = function (line, dontUpdateContextPosition, index) {
	var height = line.getHeight();
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (context.availableHeight < height || !page) {
		return false;
	}

	line.x = context.x + (line.x || 0);
	line.y = context.y + (line.y || 0);

	this.alignLine(line);

	addPageItem(page, {
		type: 'line',
		item: line
	}, index);
	this.tracker.emit('lineAdded', line);

	if (!dontUpdateContextPosition) {
		context.moveDown(height);
	}

	return position;
};

ElementWriter.prototype.alignLine = function (line) {
	var width = this.context.availableWidth;
	var lineWidth = line.getWidth();

	var alignment = line.inlines && line.inlines.length > 0 && line.inlines[0].alignment;

	var offset = 0;
	switch (alignment) {
		case 'right':
			offset = width - lineWidth;
			break;
		case 'center':
			offset = (width - lineWidth) / 2;
			break;
	}

	if (offset) {
		line.x = (line.x || 0) + offset;
	}

	if (alignment === 'justify' &&
		!line.newLineForced &&
		!line.lastLineInParagraph &&
		line.inlines.length > 1) {
		var additionalSpacing = (width - lineWidth) / (line.inlines.length - 1);

		for (var i = 1, l = line.inlines.length; i < l; i++) {
			offset = i * additionalSpacing;

			line.inlines[i].x += offset;
			line.inlines[i].justifyShift = additionalSpacing;
		}
	}
};

ElementWriter.prototype.addImage = function (image, index, type) {
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (!page || (image.absolutePosition === undefined && context.availableHeight < image._height && page.items.length > 0)) {
		return false;
	}

	if (image._x === undefined) {
		image._x = image.x || 0;
	}

	image.x = context.x + image._x;
	image.y = context.y;

	this.alignImage(image);

	addPageItem(page, {
		type: type || 'image',
		item: image
	}, index);

	context.moveDown(image._height);

	return position;
};

ElementWriter.prototype.addSVG = function (image, index) {
	return this.addImage(image, index, 'svg');
};

ElementWriter.prototype.addQr = function (qr, index) {
	var context = this.context;
	var page = context.getCurrentPage(),
		position = this.getCurrentPositionOnPage();

	if (!page || (qr.absolutePosition === undefined && context.availableHeight < qr._height)) {
		return false;
	}

	if (qr._x === undefined) {
		qr._x = qr.x || 0;
	}

	qr.x = context.x + qr._x;
	qr.y = context.y;

	this.alignImage(qr);

	for (var i = 0, l = qr._canvas.length; i < l; i++) {
		var vector = qr._canvas[i];
		vector.x += qr.x;
		vector.y += qr.y;
		this.addVector(vector, true, true, index);
	}

	context.moveDown(qr._height);

	return position;
};

ElementWriter.prototype.alignImage = function (image) {
	var width = this.context.availableWidth;
	var imageWidth = image._minWidth;
	var offset = 0;
	switch (image._alignment) {
		case 'right':
			offset = width - imageWidth;
			break;
		case 'center':
			offset = (width - imageWidth) / 2;
			break;
	}

	if (offset) {
		image.x = (image.x || 0) + offset;
	}
};

ElementWriter.prototype.alignCanvas = function (node) {
	var width = this.context.availableWidth;
	var canvasWidth = node._minWidth;
	var offset = 0;
	switch (node._alignment) {
		case 'right':
			offset = width - canvasWidth;
			break;
		case 'center':
			offset = (width - canvasWidth) / 2;
			break;
	}
	if (offset) {
		node.canvas.forEach(function (vector) {
			offsetVector(vector, offset, 0);
		});
	}
};

ElementWriter.prototype.addVector = function (vector, ignoreContextX, ignoreContextY, index, forcePage) {
	var context = this.context;
	var page = context.getCurrentPage();
	if (isNumber(forcePage)) {
		page = context.pages[forcePage];
	}
	var position = this.getCurrentPositionOnPage();

	if (page) {
		offsetVector(vector, ignoreContextX ? 0 : context.x, ignoreContextY ? 0 : context.y);
		addPageItem(page, {
			type: 'vector',
			item: vector
		}, index);
		return position;
	}
};

ElementWriter.prototype.beginClip = function (width, height) {
	var ctx = this.context;
	var page = ctx.getCurrentPage();
	page.items.push({
		type: 'beginClip',
		item: { x: ctx.x, y: ctx.y, width: width, height: height }
	});
	return true;
};

ElementWriter.prototype.endClip = function () {
	var ctx = this.context;
	var page = ctx.getCurrentPage();
	page.items.push({
		type: 'endClip'
	});
	return true;
};

function cloneLine(line) {
	var result = new Line(line.maxWidth);

	for (var key in line) {
		if (line.hasOwnProperty(key)) {
			result[key] = line[key];
		}
	}

	return result;
}

ElementWriter.prototype.addFragment = function (block, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition) {
	var ctx = this.context;
	var page = ctx.getCurrentPage();

	if (!useBlockXOffset && block.height > ctx.availableHeight) {
		return false;
	}

	block.items.forEach(function (item) {
		switch (item.type) {
			case 'line':
				var l = cloneLine(item.item);

				if (l._node) {
					l._node.positions[0].pageNumber = ctx.page + 1;
				}
				l.x = (l.x || 0) + (useBlockXOffset ? (block.xOffset || 0) : ctx.x);
				l.y = (l.y || 0) + (useBlockYOffset ? (block.yOffset || 0) : ctx.y);

				page.items.push({
					type: 'line',
					item: l
				});
				break;

			case 'vector':
				var v = pack(item.item);

				offsetVector(v, useBlockXOffset ? (block.xOffset || 0) : ctx.x, useBlockYOffset ? (block.yOffset || 0) : ctx.y);
				if (v._isFillColorFromUnbreakable) {
					// If the item is a fillColor from an unbreakable block
					// We have to add it at the beginning of the items body array of the page
					delete v._isFillColorFromUnbreakable;
					const endOfBackgroundItemsIndex = ctx.backgroundLength[ctx.page];
					page.items.splice(endOfBackgroundItemsIndex, 0, {
						type: 'vector',
						item: v
					});
				} else {
					page.items.push({
						type: 'vector',
						item: v
					});
				}
				break;

			case 'image':
			case 'svg':
				var img = pack(item.item);

				img.x = (img.x || 0) + (useBlockXOffset ? (block.xOffset || 0) : ctx.x);
				img.y = (img.y || 0) + (useBlockYOffset ? (block.yOffset || 0) : ctx.y);

				page.items.push({
					type: item.type,
					item: img
				});
				break;
		}
	});

	if (!dontUpdateContextPosition) {
		ctx.moveDown(block.height);
	}

	return true;
};

/**
 * Pushes the provided context onto the stack or creates a new one
 *
 * pushContext(context) - pushes the provided context and makes it current
 * pushContext(width, height) - creates and pushes a new context with the specified width and height
 * pushContext() - creates a new context for unbreakable blocks (with current availableWidth and full-page-height)
 */
ElementWriter.prototype.pushContext = function (contextOrWidth, height) {
	if (contextOrWidth === undefined) {
		height = this.context.getCurrentPage().height - this.context.pageMargins.top - this.context.pageMargins.bottom;
		contextOrWidth = this.context.availableWidth;
	}

	if (isNumber(contextOrWidth)) {
		contextOrWidth = new DocumentContext({ width: contextOrWidth, height: height }, { left: 0, right: 0, top: 0, bottom: 0 });
	}

	this.contextStack.push(this.context);
	this.context = contextOrWidth;
};

ElementWriter.prototype.popContext = function () {
	this.context = this.contextStack.pop();
};

ElementWriter.prototype.getCurrentPositionOnPage = function () {
	return (this.contextStack[0] || this.context).getCurrentPosition();
};


module.exports = ElementWriter;


/***/ }),

/***/ 28284:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isArray = (__webpack_require__(91867).isArray);

function typeName(bold, italics) {
	var type = 'normal';
	if (bold && italics) {
		type = 'bolditalics';
	} else if (bold) {
		type = 'bold';
	} else if (italics) {
		type = 'italics';
	}
	return type;
}

function FontProvider(fontDescriptors, pdfKitDoc) {
	this.fonts = {};
	this.pdfKitDoc = pdfKitDoc;
	this.fontCache = {};

	for (var font in fontDescriptors) {
		if (fontDescriptors.hasOwnProperty(font)) {
			var fontDef = fontDescriptors[font];

			this.fonts[font] = {
				normal: fontDef.normal,
				bold: fontDef.bold,
				italics: fontDef.italics,
				bolditalics: fontDef.bolditalics
			};
		}
	}
}

FontProvider.prototype.getFontType = function (bold, italics) {
	return typeName(bold, italics);
};

FontProvider.prototype.getFontFile = function (familyName, bold, italics) {
	var type = this.getFontType(bold, italics);
	if (!this.fonts[familyName] || !this.fonts[familyName][type]) {
		return null;
	}

	return this.fonts[familyName][type];
};

FontProvider.prototype.provideFont = function (familyName, bold, italics) {
	var type = this.getFontType(bold, italics);
	if (this.getFontFile(familyName, bold, italics) === null) {
		throw new Error('Font \'' + familyName + '\' in style \'' + type + '\' is not defined in the font section of the document definition.');
	}

	this.fontCache[familyName] = this.fontCache[familyName] || {};

	if (!this.fontCache[familyName][type]) {
		var def = this.fonts[familyName][type];
		if (!isArray(def)) {
			def = [def];
		}
		this.fontCache[familyName][type] = this.pdfKitDoc.font.apply(this.pdfKitDoc, def)._font;
	}

	return this.fontCache[familyName][type];
};

module.exports = FontProvider;


/***/ }),

/***/ 91867:
/***/ (function(module) {

"use strict";


function isString(variable) {
	return typeof variable === 'string' || variable instanceof String;
}

function isNumber(variable) {
	return typeof variable === 'number' || variable instanceof Number;
}

function isBoolean(variable) {
	return typeof variable === 'boolean';
}

function isArray(variable) {
	return Array.isArray(variable);
}

function isFunction(variable) {
	return typeof variable === 'function';
}

function isObject(variable) {
	return variable !== null && typeof variable === 'object';
}

function isNull(variable) {
	return variable === null;
}

function isUndefined(variable) {
	return variable === undefined;
}

/**
 * @param {any} variable
 * @returns {boolean}
 */
function isPositiveInteger(variable) {
	if (!isNumber(variable) || !Number.isInteger(variable) || variable <= 0) {
		return false;
	}
	return true;
}

function pack() {
	var result = {};

	for (var i = 0, l = arguments.length; i < l; i++) {
		var obj = arguments[i];

		if (obj) {
			for (var key in obj) {
				if (obj.hasOwnProperty(key)) {
					result[key] = obj[key];
				}
			}
		}
	}

	return result;
}

function offsetVector(vector, x, y) {
	switch (vector.type) {
		case 'ellipse':
		case 'rect':
			vector.x += x;
			vector.y += y;
			break;
		case 'line':
			vector.x1 += x;
			vector.x2 += x;
			vector.y1 += y;
			vector.y2 += y;
			break;
		case 'polyline':
			for (var i = 0, l = vector.points.length; i < l; i++) {
				vector.points[i].x += x;
				vector.points[i].y += y;
			}
			break;
	}
}

function fontStringify(key, val) {
	if (key === 'font') {
		return 'font';
	}
	return val;
}

function getNodeId(node) {
	if (node.id) {
		return node.id;
	}

	if (isArray(node.text)) {
		for (var i = 0, l = node.text.length; i < l; i++) {
			var n = node.text[i];
			var nodeId = getNodeId(n);
			if (nodeId) {
				return nodeId;
			}
		}
	}

	return null;
}

function isPattern(color) {
	return isArray(color) && color.length === 2;
}

// converts from a [<pattern name>, <color>] as used by pdfmake
// into [<pattern object>, <color>] as used by pdfkit
// (the pattern has to be registered in the doc definition of course)
function getPattern(color, patterns) {
	return [patterns[color[0]], color[1]];
}

module.exports = {
	isString: isString,
	isNumber: isNumber,
	isBoolean: isBoolean,
	isArray: isArray,
	isFunction: isFunction,
	isObject: isObject,
	isNull: isNull,
	isUndefined: isUndefined,
	isPositiveInteger: isPositiveInteger,
	pack: pack,
	fontStringify: fontStringify,
	offsetVector: offsetVector,
	getNodeId: getNodeId,
	isPattern: isPattern,
	getPattern: getPattern
};


/***/ }),

/***/ 93415:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


var fs = __webpack_require__(48181);

function ImageMeasure(pdfKitDoc, imageDictionary) {
	this.pdfKitDoc = pdfKitDoc;
	this.imageDictionary = imageDictionary || {};
}

ImageMeasure.prototype.measureImage = function (src) {
	var image;
	var that = this;

	if (!this.pdfKitDoc._imageRegistry[src]) {
		try {
			image = this.pdfKitDoc.openImage(realImageSrc(src));
			if (!image) {
				throw 'No image';
			}
		} catch (error) {
			throw 'Invalid image: ' + error.toString() + '\nImages dictionary should contain dataURL entries (or local file paths in node.js)';
		}
		image.embed(this.pdfKitDoc);
		this.pdfKitDoc._imageRegistry[src] = image;
	} else {
		image = this.pdfKitDoc._imageRegistry[src];
	}

	var imageSize = { width: image.width, height: image.height };

	// If EXIF orientation calls for it, swap width and height
	if (image.orientation > 4) {
		imageSize = { width: image.height, height: image.width };
	}

	return imageSize;

	function realImageSrc(src) {
		var img = that.imageDictionary[src];

		if (!img) {
			return src;
		}

		if (typeof img === 'object') {
			throw 'Not supported image definition: ' + JSON.stringify(img);
		}

		if (fs.existsSync(img)) {
			return fs.readFileSync(img);
		}

		var index = img.indexOf('base64,');
		if (index < 0) {
			return that.imageDictionary[src];
		}

		return Buffer.from(img.substring(index + 7), 'base64');
	}
};

module.exports = ImageMeasure;


/***/ }),

/***/ 70770:
/***/ (function(module) {

"use strict";


/**
 * Creates an instance of Line
 *
 * @constructor
 * @this {Line}
 * @param {Number} Maximum width this line can have
 */
function Line(maxWidth) {
	this.maxWidth = maxWidth;
	this.leadingCut = 0;
	this.trailingCut = 0;
	this.inlineWidths = 0;
	this.inlines = [];
}

Line.prototype.getAscenderHeight = function () {
	var y = 0;

	this.inlines.forEach(function (inline) {
		y = Math.max(y, inline.font.ascender / 1000 * inline.fontSize);
	});
	return y;
};

Line.prototype.hasEnoughSpaceForInline = function (inline, nextInlines) {
	nextInlines = nextInlines || [];

	if (this.inlines.length === 0) {
		return true;
	}
	if (this.newLineForced) {
		return false;
	}

	var inlineWidth = inline.width;
	var inlineTrailingCut = inline.trailingCut || 0;
	if (inline.noNewLine) {
		for (var i = 0, l = nextInlines.length; i < l; i++) {
			var nextInline = nextInlines[i];
			inlineWidth += nextInline.width;
			inlineTrailingCut += nextInline.trailingCut || 0;
			if (!nextInline.noNewLine) {
				break;
			}
		}
	}

	return (this.inlineWidths + inlineWidth - this.leadingCut - inlineTrailingCut) <= this.maxWidth;
};

Line.prototype.addInline = function (inline) {
	if (this.inlines.length === 0) {
		this.leadingCut = inline.leadingCut || 0;
	}
	this.trailingCut = inline.trailingCut || 0;

	inline.x = this.inlineWidths - this.leadingCut;

	this.inlines.push(inline);
	this.inlineWidths += inline.width;

	if (inline.lineEnd) {
		this.newLineForced = true;
	}
};

Line.prototype.getWidth = function () {
	return this.inlineWidths - this.leadingCut - this.trailingCut;
};

Line.prototype.getAvailableWidth = function () {
	return this.maxWidth - this.getWidth();
};

/**
 * Returns line height
 * @return {Number}
 */
Line.prototype.getHeight = function () {
	var max = 0;

	this.inlines.forEach(function (item) {
		max = Math.max(max, item.height || 0);
	});

	return max;
};

module.exports = Line;


/***/ }),

/***/ 11220:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isUndefined = (__webpack_require__(91867).isUndefined);
var ElementWriter = __webpack_require__(54861);

/**
 * Creates an instance of PageElementWriter - an extended ElementWriter
 * which can handle:
 * - page-breaks (it adds new pages when there's not enough space left),
 * - repeatable fragments (like table-headers, which are repeated everytime
 *                         a page-break occurs)
 * - transactions (used for unbreakable-blocks when we want to make sure
 *                 whole block will be rendered on the same page)
 */
function PageElementWriter(context, tracker) {
	this.transactionLevel = 0;
	this.repeatables = [];
	this.tracker = tracker;
	this.writer = new ElementWriter(context, tracker);
}

function fitOnPage(self, addFct) {
	var position = addFct(self);
	if (!position) {
		self.moveToNextPage();
		position = addFct(self);
	}
	return position;
}

PageElementWriter.prototype.addLine = function (line, dontUpdateContextPosition, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addLine(line, dontUpdateContextPosition, index);
	});
};

PageElementWriter.prototype.addImage = function (image, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addImage(image, index);
	});
};

PageElementWriter.prototype.addSVG = function (image, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addSVG(image, index);
	});
};

PageElementWriter.prototype.addQr = function (qr, index) {
	return fitOnPage(this, function (self) {
		return self.writer.addQr(qr, index);
	});
};

PageElementWriter.prototype.addVector = function (vector, ignoreContextX, ignoreContextY, index, forcePage) {
	return this.writer.addVector(vector, ignoreContextX, ignoreContextY, index, forcePage);
};

PageElementWriter.prototype.beginClip = function (width, height) {
	return this.writer.beginClip(width, height);
};

PageElementWriter.prototype.endClip = function () {
	return this.writer.endClip();
};

PageElementWriter.prototype.alignCanvas = function (node) {
	this.writer.alignCanvas(node);
};

PageElementWriter.prototype.addFragment = function (fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition) {
	if (!this.writer.addFragment(fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition)) {
		this.moveToNextPage();
		this.writer.addFragment(fragment, useBlockXOffset, useBlockYOffset, dontUpdateContextPosition);
	}
};

PageElementWriter.prototype.moveToNextPage = function (pageOrientation) {

	var nextPage = this.writer.context.moveToNextPage(pageOrientation);

	// moveToNextPage is called multiple times for table, because is called for each column
	// and repeatables are inserted only in the first time. If columns are used, is needed
	// call for table in first column and then for table in the second column (is other repeatables).
	this.repeatables.forEach(function (rep) {
		if (isUndefined(rep.insertedOnPages[this.writer.context.page])) {
			rep.insertedOnPages[this.writer.context.page] = true;
			this.writer.addFragment(rep, true);
		} else {
			this.writer.context.moveDown(rep.height);
		}
	}, this);

	this.writer.tracker.emit('pageChanged', {
		prevPage: nextPage.prevPage,
		prevY: nextPage.prevY,
		y: this.writer.context.y
	});
};

PageElementWriter.prototype.beginUnbreakableBlock = function (width, height) {
	if (this.transactionLevel++ === 0) {
		this.originalX = this.writer.context.x;
		this.writer.pushContext(width, height);
	}
};

PageElementWriter.prototype.commitUnbreakableBlock = function (forcedX, forcedY) {
	if (--this.transactionLevel === 0) {
		var unbreakableContext = this.writer.context;
		this.writer.popContext();

		var nbPages = unbreakableContext.pages.length;
		if (nbPages > 0) {
			// no support for multi-page unbreakableBlocks
			var fragment = unbreakableContext.pages[0];
			fragment.xOffset = forcedX;
			fragment.yOffset = forcedY;

			//TODO: vectors can influence height in some situations
			if (nbPages > 1) {
				// on out-of-context blocs (headers, footers, background) height should be the whole DocumentContext height
				if (forcedX !== undefined || forcedY !== undefined) {
					fragment.height = unbreakableContext.getCurrentPage().pageSize.height - unbreakableContext.pageMargins.top - unbreakableContext.pageMargins.bottom;
				} else {
					fragment.height = this.writer.context.getCurrentPage().pageSize.height - this.writer.context.pageMargins.top - this.writer.context.pageMargins.bottom;
					for (var i = 0, l = this.repeatables.length; i < l; i++) {
						fragment.height -= this.repeatables[i].height;
					}
				}
			} else {
				fragment.height = unbreakableContext.y;
			}

			if (forcedX !== undefined || forcedY !== undefined) {
				this.writer.addFragment(fragment, true, true, true);
			} else {
				this.addFragment(fragment);
			}
		}
	}
};

PageElementWriter.prototype.currentBlockToRepeatable = function () {
	var unbreakableContext = this.writer.context;
	var rep = { items: [] };

	unbreakableContext.pages[0].items.forEach(function (item) {
		rep.items.push(item);
	});

	rep.xOffset = this.originalX;

	//TODO: vectors can influence height in some situations
	rep.height = unbreakableContext.y;

	rep.insertedOnPages = [];

	return rep;
};

PageElementWriter.prototype.pushToRepeatables = function (rep) {
	this.repeatables.push(rep);
};

PageElementWriter.prototype.popFromRepeatables = function () {
	this.repeatables.pop();
};

PageElementWriter.prototype.context = function () {
	return this.writer.context;
};

module.exports = PageElementWriter;


/***/ }),

/***/ 85208:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


function _interopDefault(ex) {
	return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex;
}

var PdfKit = _interopDefault(__webpack_require__(30357));

function getEngineInstance() {
	return PdfKit;
}

function createPdfDocument(options) {
	options = options || {};
	return new PdfKit(options);
}

module.exports = {
	getEngineInstance: getEngineInstance,
	createPdfDocument: createPdfDocument
};


/***/ }),

/***/ 81566:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/


var PdfKitEngine = __webpack_require__(85208);
var FontProvider = __webpack_require__(28284);
var LayoutBuilder = __webpack_require__(65300);
var sizes = __webpack_require__(70098);
var ImageMeasure = __webpack_require__(93415);
var SVGMeasure = __webpack_require__(89638);
var textDecorator = __webpack_require__(84786);
var TextTools = __webpack_require__(11548);
var isFunction = (__webpack_require__(91867).isFunction);
var isString = (__webpack_require__(91867).isString);
var isNumber = (__webpack_require__(91867).isNumber);
var isBoolean = (__webpack_require__(91867).isBoolean);
var isArray = (__webpack_require__(91867).isArray);
var isUndefined = (__webpack_require__(91867).isUndefined);
var isPattern = (__webpack_require__(91867).isPattern);
var getPattern = (__webpack_require__(91867).getPattern);
var SVGtoPDF = __webpack_require__(5557);

var findFont = function (fonts, requiredFonts, defaultFont) {
	for (var i = 0; i < requiredFonts.length; i++) {
		var requiredFont = requiredFonts[i].toLowerCase();

		for (var font in fonts) {
			if (font.toLowerCase() === requiredFont) {
				return font;
			}
		}
	}

	return defaultFont;
};

////////////////////////////////////////
// PdfPrinter

/**
 * @class Creates an instance of a PdfPrinter which turns document definition into a pdf
 *
 * @param {Object} fontDescriptors font definition dictionary
 *
 * @example
 * var fontDescriptors = {
 *	Roboto: {
 *		normal: 'fonts/Roboto-Regular.ttf',
 *		bold: 'fonts/Roboto-Medium.ttf',
 *		italics: 'fonts/Roboto-Italic.ttf',
 *		bolditalics: 'fonts/Roboto-MediumItalic.ttf'
 *	}
 * };
 *
 * var printer = new PdfPrinter(fontDescriptors);
 */
function PdfPrinter(fontDescriptors) {
	this.fontDescriptors = fontDescriptors;
}

/**
 * Executes layout engine for the specified document and renders it into a pdfkit document
 * ready to be saved.
 *
 * @param {Object} docDefinition document definition
 * @param {Object} docDefinition.content an array describing the pdf structure (for more information take a look at the examples in the /examples folder)
 * @param {Object} [docDefinition.defaultStyle] default (implicit) style definition
 * @param {Object} [docDefinition.styles] dictionary defining all styles which can be used in the document
 * @param {Object} [docDefinition.pageSize] page size (pdfkit units, A4 dimensions by default)
 * @param {Number} docDefinition.pageSize.width width
 * @param {Number} docDefinition.pageSize.height height
 * @param {Object} [docDefinition.pageMargins] page margins (pdfkit units)
 * @param {Number} docDefinition.maxPagesNumber maximum number of pages to render
 *
 * @example
 *
 * var docDefinition = {
 * 	info: {
 *		title: 'awesome Document',
 *		author: 'john doe',
 *		subject: 'subject of document',
 *		keywords: 'keywords for document',
 * 	},
 *	content: [
 *		'First paragraph',
 *		'Second paragraph, this time a little bit longer',
 *		{ text: 'Third paragraph, slightly bigger font size', fontSize: 20 },
 *		{ text: 'Another paragraph using a named style', style: 'header' },
 *		{ text: ['playing with ', 'inlines' ] },
 *		{ text: ['and ', { text: 'restyling ', bold: true }, 'them'] },
 *	],
 *	styles: {
 *		header: { fontSize: 30, bold: true }
 *	},
 *	patterns: {
 *		stripe45d: {
 *			boundingBox: [1, 1, 4, 4],
 *			xStep: 3,
 *			yStep: 3,
 *			pattern: '1 w 0 1 m 4 5 l s 2 0 m 5 3 l s'
 *		}
 *	}
 * };
 *
 * var pdfKitDoc = printer.createPdfKitDocument(docDefinition);
 *
 * pdfKitDoc.pipe(fs.createWriteStream('sample.pdf'));
 * pdfKitDoc.end();
 *
 * @return {Object} a pdfKit document object which can be saved or encode to data-url
 */
PdfPrinter.prototype.createPdfKitDocument = function (docDefinition, options) {
	options = options || {};

	docDefinition.version = docDefinition.version || '1.3';
	docDefinition.subset = docDefinition.subset || undefined;
	docDefinition.tagged = typeof docDefinition.tagged === 'boolean' ? docDefinition.tagged : false;
	docDefinition.displayTitle = typeof docDefinition.displayTitle === 'boolean' ? docDefinition.displayTitle : false;
	docDefinition.compress = isBoolean(docDefinition.compress) ? docDefinition.compress : true;
	docDefinition.images = docDefinition.images || {};
	docDefinition.pageMargins = ((docDefinition.pageMargins !== undefined) && (docDefinition.pageMargins !== null)) ? docDefinition.pageMargins : 40;

	var pageSize = fixPageSize(docDefinition.pageSize, docDefinition.pageOrientation);

	var pdfOptions = {
		size: [pageSize.width, pageSize.height],
		pdfVersion: docDefinition.version,
		subset: docDefinition.subset,
		tagged: docDefinition.tagged,
		displayTitle: docDefinition.displayTitle,
		compress: docDefinition.compress,
		userPassword: docDefinition.userPassword,
		ownerPassword: docDefinition.ownerPassword,
		permissions: docDefinition.permissions,
		lang: docDefinition.language,
		fontLayoutCache: isBoolean(options.fontLayoutCache) ? options.fontLayoutCache : true,
		bufferPages: options.bufferPages || false,
		autoFirstPage: false,
		info: createMetadata(docDefinition),
		font: null
	};

	this.pdfKitDoc = PdfKitEngine.createPdfDocument(pdfOptions);

	this.fontProvider = new FontProvider(this.fontDescriptors, this.pdfKitDoc);

	var builder = new LayoutBuilder(pageSize, fixPageMargins(docDefinition.pageMargins), new ImageMeasure(this.pdfKitDoc, docDefinition.images), new SVGMeasure());

	registerDefaultTableLayouts(builder);
	if (options.tableLayouts) {
		builder.registerTableLayouts(options.tableLayouts);
	}

	var pages = builder.layoutDocument(docDefinition.content, this.fontProvider, docDefinition.styles || {}, docDefinition.defaultStyle || {
		fontSize: 12,
		font: 'Roboto'
	}, docDefinition.background, docDefinition.header, docDefinition.footer, docDefinition.images, docDefinition.watermark, docDefinition.pageBreakBefore);
	var maxNumberPages = docDefinition.maxPagesNumber || -1;
	if (isNumber(maxNumberPages) && maxNumberPages > -1) {
		pages = pages.slice(0, maxNumberPages);
	}

	// if pageSize.height is set to Infinity, calculate the actual height of the page that
	// was laid out using the height of each of the items in the page.
	if (pageSize.height === Infinity) {
		var pageHeight = calculatePageHeight(pages, docDefinition.pageMargins);
		this.pdfKitDoc.options.size = [pageSize.width, pageHeight];
	}

	var patterns = createPatterns(docDefinition.patterns || {}, this.pdfKitDoc);

	renderPages(pages, this.fontProvider, this.pdfKitDoc, patterns, options.progressCallback);

	if (options.autoPrint) {
		var printActionRef = this.pdfKitDoc.ref({
			Type: 'Action',
			S: 'Named',
			N: 'Print'
		});
		this.pdfKitDoc._root.data.OpenAction = printActionRef;
		printActionRef.end();
	}
	return this.pdfKitDoc;
};

function createMetadata(docDefinition) {
	// PDF standard has these properties reserved: Title, Author, Subject, Keywords,
	// Creator, Producer, CreationDate, ModDate, Trapped.
	// To keep the pdfmake api consistent, the info field are defined lowercase.
	// Custom properties don't contain a space.
	function standardizePropertyKey(key) {
		var standardProperties = ['Title', 'Author', 'Subject', 'Keywords',
			'Creator', 'Producer', 'CreationDate', 'ModDate', 'Trapped'];
		var standardizedKey = key.charAt(0).toUpperCase() + key.slice(1);
		if (standardProperties.indexOf(standardizedKey) !== -1) {
			return standardizedKey;
		}

		return key.replace(/\s+/g, '');
	}

	var info = {
		Producer: 'pdfmake',
		Creator: 'pdfmake'
	};

	if (docDefinition.info) {
		for (var key in docDefinition.info) {
			var value = docDefinition.info[key];
			if (value) {
				key = standardizePropertyKey(key);
				info[key] = value;
			}
		}
	}
	return info;
}

function calculatePageHeight(pages, margins) {
	function getItemHeight(item) {
		if (isFunction(item.item.getHeight)) {
			return item.item.getHeight();
		} else if (item.item._height) {
			return item.item._height;
		} else if (item.type === 'vector') {
			if (typeof item.item.y1 !== 'undefined') {
				return item.item.y1 > item.item.y2 ? item.item.y1 : item.item.y2;
			} else {
				return item.item.h;
			}
		} else {
			// TODO: add support for next item types
			return 0;
		}
	}

	function getBottomPosition(item) {
		var top = item.item.y || 0;
		var height = getItemHeight(item);
		return top + height;
	}

	var fixedMargins = fixPageMargins(margins || 40);
	var height = fixedMargins.top;

	pages.forEach(function (page) {
		page.items.forEach(function (item) {
			var bottomPosition = getBottomPosition(item);
			if (bottomPosition > height) {
				height = bottomPosition;
			}
		});
	});

	height += fixedMargins.bottom;

	return height;
}

function fixPageSize(pageSize, pageOrientation) {
	function isNeedSwapPageSizes(pageOrientation) {
		if (isString(pageOrientation)) {
			pageOrientation = pageOrientation.toLowerCase();
			return ((pageOrientation === 'portrait') && (size.width > size.height)) ||
				((pageOrientation === 'landscape') && (size.width < size.height));
		}
		return false;
	}

	// if pageSize.height is set to auto, set the height to infinity so there are no page breaks.
	if (pageSize && pageSize.height === 'auto') {
		pageSize.height = Infinity;
	}

	var size = pageSize2widthAndHeight(pageSize || 'A4');
	if (isNeedSwapPageSizes(pageOrientation)) { // swap page sizes
		size = { width: size.height, height: size.width };
	}
	size.orientation = size.width > size.height ? 'landscape' : 'portrait';
	return size;
}

function fixPageMargins(margin) {
	if (isNumber(margin)) {
		margin = { left: margin, right: margin, top: margin, bottom: margin };
	} else if (isArray(margin)) {
		if (margin.length === 2) {
			margin = { left: margin[0], top: margin[1], right: margin[0], bottom: margin[1] };
		} else if (margin.length === 4) {
			margin = { left: margin[0], top: margin[1], right: margin[2], bottom: margin[3] };
		} else {
			throw 'Invalid pageMargins definition';
		}
	}

	return margin;
}

function registerDefaultTableLayouts(layoutBuilder) {
	layoutBuilder.registerTableLayouts({
		noBorders: {
			hLineWidth: function (i) {
				return 0;
			},
			vLineWidth: function (i) {
				return 0;
			},
			paddingLeft: function (i) {
				return i && 4 || 0;
			},
			paddingRight: function (i, node) {
				return (i < node.table.widths.length - 1) ? 4 : 0;
			}
		},
		headerLineOnly: {
			hLineWidth: function (i, node) {
				if (i === 0 || i === node.table.body.length) {
					return 0;
				}
				return (i === node.table.headerRows) ? 2 : 0;
			},
			vLineWidth: function (i) {
				return 0;
			},
			paddingLeft: function (i) {
				return i === 0 ? 0 : 8;
			},
			paddingRight: function (i, node) {
				return (i === node.table.widths.length - 1) ? 0 : 8;
			}
		},
		lightHorizontalLines: {
			hLineWidth: function (i, node) {
				if (i === 0 || i === node.table.body.length) {
					return 0;
				}
				return (i === node.table.headerRows) ? 2 : 1;
			},
			vLineWidth: function (i) {
				return 0;
			},
			hLineColor: function (i) {
				return i === 1 ? 'black' : '#aaa';
			},
			paddingLeft: function (i) {
				return i === 0 ? 0 : 8;
			},
			paddingRight: function (i, node) {
				return (i === node.table.widths.length - 1) ? 0 : 8;
			}
		}
	});
}

function pageSize2widthAndHeight(pageSize) {
	if (isString(pageSize)) {
		var size = sizes[pageSize.toUpperCase()];
		if (!size) {
			throw 'Page size ' + pageSize + ' not recognized';
		}
		return { width: size[0], height: size[1] };
	}

	return pageSize;
}

function updatePageOrientationInOptions(currentPage, pdfKitDoc) {
	var previousPageOrientation = pdfKitDoc.options.size[0] > pdfKitDoc.options.size[1] ? 'landscape' : 'portrait';

	if (currentPage.pageSize.orientation !== previousPageOrientation) {
		var width = pdfKitDoc.options.size[0];
		var height = pdfKitDoc.options.size[1];
		pdfKitDoc.options.size = [height, width];
	}
}

function renderPages(pages, fontProvider, pdfKitDoc, patterns, progressCallback) {
	pdfKitDoc._pdfMakePages = pages;
	pdfKitDoc.addPage();

	var totalItems = 0;
	if (progressCallback) {
		pages.forEach(function (page) {
			totalItems += page.items.length;
		});
	}

	var renderedItems = 0;
	progressCallback = progressCallback || function () {
	};

	for (var i = 0; i < pages.length; i++) {
		if (i > 0) {
			updatePageOrientationInOptions(pages[i], pdfKitDoc);
			pdfKitDoc.addPage(pdfKitDoc.options);
		}

		var page = pages[i];
		for (var ii = 0, il = page.items.length; ii < il; ii++) {
			var item = page.items[ii];
			switch (item.type) {
				case 'vector':
					renderVector(item.item, patterns, pdfKitDoc);
					break;
				case 'line':
					renderLine(item.item, item.item.x, item.item.y, patterns, pdfKitDoc);
					break;
				case 'image':
					renderImage(item.item, item.item.x, item.item.y, pdfKitDoc);
					break;
				case 'svg':
					renderSVG(item.item, item.item.x, item.item.y, pdfKitDoc, fontProvider);
					break;
				case 'beginClip':
					beginClip(item.item, pdfKitDoc);
					break;
				case 'endClip':
					endClip(pdfKitDoc);
					break;
			}
			renderedItems++;
			progressCallback(renderedItems / totalItems);
		}
		if (page.watermark) {
			renderWatermark(page, pdfKitDoc);
		}
	}
}

/**
 * Shift the "y" height of the text baseline up or down (superscript or subscript,
 * respectively). The exact shift can / should be changed according to standard
 * conventions.
 *
 * @param {number} y
 * @param {any} inline
 */
function offsetText(y, inline) {
	var newY = y;
	if (inline.sup) {
		newY -= inline.fontSize * 0.75;
	}
	if (inline.sub) {
		newY += inline.fontSize * 0.35;
	}
	return newY;
}

function renderLine(line, x, y, patterns, pdfKitDoc) {
	function preparePageNodeRefLine(_pageNodeRef, inline) {
		var newWidth;
		var diffWidth;
		var textTools = new TextTools(null);

		if (isUndefined(_pageNodeRef.positions)) {
			throw 'Page reference id not found';
		}

		var pageNumber = _pageNodeRef.positions[0].pageNumber.toString();

		inline.text = pageNumber;
		newWidth = textTools.widthOfString(inline.text, inline.font, inline.fontSize, inline.characterSpacing, inline.fontFeatures);
		diffWidth = inline.width - newWidth;
		inline.width = newWidth;

		switch (inline.alignment) {
			case 'right':
				inline.x += diffWidth;
				break;
			case 'center':
				inline.x += diffWidth / 2;
				break;
		}
	}

	if (line._pageNodeRef) {
		preparePageNodeRefLine(line._pageNodeRef, line.inlines[0]);
	}

	x = x || 0;
	y = y || 0;

	var lineHeight = line.getHeight();
	var ascenderHeight = line.getAscenderHeight();
	var descent = lineHeight - ascenderHeight;

	textDecorator.drawBackground(line, x, y, patterns, pdfKitDoc);

	//TODO: line.optimizeInlines();
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		var shiftToBaseline = lineHeight - ((inline.font.ascender / 1000) * inline.fontSize) - descent;

		if (inline._pageNodeRef) {
			preparePageNodeRefLine(inline._pageNodeRef, inline);
		}

		var options = {
			lineBreak: false,
			textWidth: inline.width,
			characterSpacing: inline.characterSpacing,
			wordCount: 1,
			link: inline.link
		};

		if (inline.linkToDestination) {
			options.goTo = inline.linkToDestination;
		}

		if (line.id && i === 0) {
			options.destination = line.id;
		}

		if (inline.fontFeatures) {
			options.features = inline.fontFeatures;
		}

		var opacity = isNumber(inline.opacity) ? inline.opacity : 1;
		pdfKitDoc.opacity(opacity);
		pdfKitDoc.fill(inline.color || 'black');

		pdfKitDoc._font = inline.font;
		pdfKitDoc.fontSize(inline.fontSize);

		var shiftedY = offsetText(y + shiftToBaseline, inline);
		pdfKitDoc.text(inline.text, x + inline.x, shiftedY, options);

		if (inline.linkToPage) {
			var _ref = pdfKitDoc.ref({ Type: 'Action', S: 'GoTo', D: [inline.linkToPage, 0, 0] }).end();
			pdfKitDoc.annotate(x + inline.x, shiftedY, inline.width, inline.height, {
				Subtype: 'Link',
				Dest: [inline.linkToPage - 1, 'XYZ', null, null, null]
			});
		}

	}
	// Decorations won't draw correctly for superscript
	textDecorator.drawDecorations(line, x, y, pdfKitDoc);
}

function renderWatermark(page, pdfKitDoc) {
	var watermark = page.watermark;

	pdfKitDoc.fill(watermark.color);
	pdfKitDoc.opacity(watermark.opacity);

	pdfKitDoc.save();

	pdfKitDoc.rotate(watermark.angle, { origin: [pdfKitDoc.page.width / 2, pdfKitDoc.page.height / 2] });

	var x = pdfKitDoc.page.width / 2 - watermark._size.size.width / 2;
	var y = pdfKitDoc.page.height / 2 - watermark._size.size.height / 2;

	pdfKitDoc._font = watermark.font;
	pdfKitDoc.fontSize(watermark.fontSize);
	pdfKitDoc.text(watermark.text, x, y, { lineBreak: false });

	pdfKitDoc.restore();
}

function renderVector(vector, patterns, pdfKitDoc) {
	//TODO: pdf optimization (there's no need to write all properties everytime)
	pdfKitDoc.lineWidth(vector.lineWidth || 1);
	if (vector.dash) {
		pdfKitDoc.dash(vector.dash.length, { space: vector.dash.space || vector.dash.length, phase: vector.dash.phase || 0 });
	} else {
		pdfKitDoc.undash();
	}
	pdfKitDoc.lineJoin(vector.lineJoin || 'miter');
	pdfKitDoc.lineCap(vector.lineCap || 'butt');

	//TODO: clipping

	var gradient = null;

	switch (vector.type) {
		case 'ellipse':
			pdfKitDoc.ellipse(vector.x, vector.y, vector.r1, vector.r2);

			if (vector.linearGradient) {
				gradient = pdfKitDoc.linearGradient(vector.x - vector.r1, vector.y, vector.x + vector.r1, vector.y);
			}
			break;
		case 'rect':
			if (vector.r) {
				pdfKitDoc.roundedRect(vector.x, vector.y, vector.w, vector.h, vector.r);
			} else {
				pdfKitDoc.rect(vector.x, vector.y, vector.w, vector.h);
			}

			if (vector.linearGradient) {
				gradient = pdfKitDoc.linearGradient(vector.x, vector.y, vector.x + vector.w, vector.y);
			}
			break;
		case 'line':
			pdfKitDoc.moveTo(vector.x1, vector.y1);
			pdfKitDoc.lineTo(vector.x2, vector.y2);
			break;
		case 'polyline':
			if (vector.points.length === 0) {
				break;
			}

			pdfKitDoc.moveTo(vector.points[0].x, vector.points[0].y);
			for (var i = 1, l = vector.points.length; i < l; i++) {
				pdfKitDoc.lineTo(vector.points[i].x, vector.points[i].y);
			}

			if (vector.points.length > 1) {
				var p1 = vector.points[0];
				var pn = vector.points[vector.points.length - 1];

				if (vector.closePath || p1.x === pn.x && p1.y === pn.y) {
					pdfKitDoc.closePath();
				}
			}
			break;
		case 'path':
			pdfKitDoc.path(vector.d);
			break;
	}

	if (vector.linearGradient && gradient) {
		var step = 1 / (vector.linearGradient.length - 1);

		for (var i = 0; i < vector.linearGradient.length; i++) {
			gradient.stop(i * step, vector.linearGradient[i]);
		}

		vector.color = gradient;
	}

	if (isPattern(vector.color)) {
		vector.color = getPattern(vector.color, patterns);
	}

	var fillOpacity = isNumber(vector.fillOpacity) ? vector.fillOpacity : 1;
	var strokeOpacity = isNumber(vector.strokeOpacity) ? vector.strokeOpacity : 1;

	if (vector.color && vector.lineColor) {
		pdfKitDoc.fillColor(vector.color, fillOpacity);
		pdfKitDoc.strokeColor(vector.lineColor, strokeOpacity);
		pdfKitDoc.fillAndStroke();
	} else if (vector.color) {
		pdfKitDoc.fillColor(vector.color, fillOpacity);
		pdfKitDoc.fill();
	} else {
		pdfKitDoc.strokeColor(vector.lineColor || 'black', strokeOpacity);
		pdfKitDoc.stroke();
	}
}

function renderImage(image, x, y, pdfKitDoc) {
	var opacity = isNumber(image.opacity) ? image.opacity : 1;
	pdfKitDoc.opacity(opacity);
	if (image.cover) {
		var align = image.cover.align || 'center';
		var valign = image.cover.valign || 'center';
		var width = image.cover.width ? image.cover.width : image.width;
		var height = image.cover.height ? image.cover.height : image.height;
		pdfKitDoc.save();
		pdfKitDoc.rect(image.x, image.y, width, height).clip();
		pdfKitDoc.image(image.image, image.x, image.y, { cover: [width, height], align: align, valign: valign });
		pdfKitDoc.restore();
	} else {
		pdfKitDoc.image(image.image, image.x, image.y, { width: image._width, height: image._height });
	}
	if (image.link) {
		pdfKitDoc.link(image.x, image.y, image._width, image._height, image.link);
	}
	if (image.linkToPage) {
		pdfKitDoc.ref({ Type: 'Action', S: 'GoTo', D: [image.linkToPage, 0, 0] }).end();
		pdfKitDoc.annotate(image.x, image.y, image._width, image._height, { Subtype: 'Link', Dest: [image.linkToPage - 1, 'XYZ', null, null, null] });
	}
	if (image.linkToDestination) {
		pdfKitDoc.goTo(image.x, image.y, image._width, image._height, image.linkToDestination);
	}
}

function renderSVG(svg, x, y, pdfKitDoc, fontProvider) {
	var options = Object.assign({ width: svg._width, height: svg._height, assumePt: true }, svg.options);
	options.fontCallback = function (family, bold, italic) {
		var fontsFamily = family.split(',').map(function (f) { return f.trim().replace(/('|")/g, ''); });
		var font = findFont(fontProvider.fonts, fontsFamily, svg.font || 'Roboto');

		var fontFile = fontProvider.getFontFile(font, bold, italic);
		if (fontFile === null) {
			var type = fontProvider.getFontType(bold, italic);
			throw new Error('Font \'' + font + '\' in style \'' + type + '\' is not defined in the font section of the document definition.');
		}

		return fontFile;
	};

	SVGtoPDF(pdfKitDoc, svg.svg, svg.x, svg.y, options);

	if (svg.link) {
		pdfKitDoc.link(svg.x, svg.y, svg._width, svg._height, svg.link);
	}
	if (svg.linkToPage) {
		pdfKitDoc.ref({Type: 'Action', S: 'GoTo', D: [svg.linkToPage, 0, 0]}).end();
		pdfKitDoc.annotate(svg.x, svg.y, svg._width, svg._height, { Subtype: 'Link', Dest: [svg.linkToPage - 1, 'XYZ', null, null, null] });
	}
	if (svg.linkToDestination) {
		pdfKitDoc.goTo(svg.x, svg.y, svg._width, svg._height, svg.linkToDestination);
	}
}

function beginClip(rect, pdfKitDoc) {
	pdfKitDoc.save();
	pdfKitDoc.addContent('' + rect.x + ' ' + rect.y + ' ' + rect.width + ' ' + rect.height + ' re');
	pdfKitDoc.clip();
}

function endClip(pdfKitDoc) {
	pdfKitDoc.restore();
}

function createPatterns(patternDefinitions, pdfKitDoc) {
	var patterns = {};
	Object.keys(patternDefinitions).forEach(function (p) {
		var pattern = patternDefinitions[p];
		patterns[p] = pdfKitDoc.pattern(pattern.boundingBox, pattern.xStep, pattern.yStep, pattern.pattern, pattern.colored);
	});
	return patterns;
}

module.exports = PdfPrinter;


/***/ }),

/***/ 72155:
/***/ (function(module) {

"use strict";
/*eslint no-unused-vars: ["error", {"args": "none"}]*/
/*eslint no-redeclare: "off"*/


/* qr.js -- QR code generator in Javascript (revision 2011-01-19)
 * Written by Kang Seonghoon <public+qrjs@mearie.org>.
 *
 * This source code is in the public domain; if your jurisdiction does not
 * recognize the public domain the terms of Creative Commons CC0 license
 * apply. In the other words, you can always do what you want.
 */


// per-version information (cf. JIS X 0510:2004 pp. 30--36, 71)
//
// [0]: the degree of generator polynomial by ECC levels
// [1]: # of code blocks by ECC levels
// [2]: left-top positions of alignment patterns
//
// the number in this table (in particular, [0]) does not exactly match with
// the numbers in the specficiation. see augumenteccs below for the reason.
var VERSIONS = [
	null,
	[[10, 7, 17, 13], [1, 1, 1, 1], []],
	[[16, 10, 28, 22], [1, 1, 1, 1], [4, 16]],
	[[26, 15, 22, 18], [1, 1, 2, 2], [4, 20]],
	[[18, 20, 16, 26], [2, 1, 4, 2], [4, 24]],
	[[24, 26, 22, 18], [2, 1, 4, 4], [4, 28]],
	[[16, 18, 28, 24], [4, 2, 4, 4], [4, 32]],
	[[18, 20, 26, 18], [4, 2, 5, 6], [4, 20, 36]],
	[[22, 24, 26, 22], [4, 2, 6, 6], [4, 22, 40]],
	[[22, 30, 24, 20], [5, 2, 8, 8], [4, 24, 44]],
	[[26, 18, 28, 24], [5, 4, 8, 8], [4, 26, 48]],
	[[30, 20, 24, 28], [5, 4, 11, 8], [4, 28, 52]],
	[[22, 24, 28, 26], [8, 4, 11, 10], [4, 30, 56]],
	[[22, 26, 22, 24], [9, 4, 16, 12], [4, 32, 60]],
	[[24, 30, 24, 20], [9, 4, 16, 16], [4, 24, 44, 64]],
	[[24, 22, 24, 30], [10, 6, 18, 12], [4, 24, 46, 68]],
	[[28, 24, 30, 24], [10, 6, 16, 17], [4, 24, 48, 72]],
	[[28, 28, 28, 28], [11, 6, 19, 16], [4, 28, 52, 76]],
	[[26, 30, 28, 28], [13, 6, 21, 18], [4, 28, 54, 80]],
	[[26, 28, 26, 26], [14, 7, 25, 21], [4, 28, 56, 84]],
	[[26, 28, 28, 30], [16, 8, 25, 20], [4, 32, 60, 88]],
	[[26, 28, 30, 28], [17, 8, 25, 23], [4, 26, 48, 70, 92]],
	[[28, 28, 24, 30], [17, 9, 34, 23], [4, 24, 48, 72, 96]],
	[[28, 30, 30, 30], [18, 9, 30, 25], [4, 28, 52, 76, 100]],
	[[28, 30, 30, 30], [20, 10, 32, 27], [4, 26, 52, 78, 104]],
	[[28, 26, 30, 30], [21, 12, 35, 29], [4, 30, 56, 82, 108]],
	[[28, 28, 30, 28], [23, 12, 37, 34], [4, 28, 56, 84, 112]],
	[[28, 30, 30, 30], [25, 12, 40, 34], [4, 32, 60, 88, 116]],
	[[28, 30, 30, 30], [26, 13, 42, 35], [4, 24, 48, 72, 96, 120]],
	[[28, 30, 30, 30], [28, 14, 45, 38], [4, 28, 52, 76, 100, 124]],
	[[28, 30, 30, 30], [29, 15, 48, 40], [4, 24, 50, 76, 102, 128]],
	[[28, 30, 30, 30], [31, 16, 51, 43], [4, 28, 54, 80, 106, 132]],
	[[28, 30, 30, 30], [33, 17, 54, 45], [4, 32, 58, 84, 110, 136]],
	[[28, 30, 30, 30], [35, 18, 57, 48], [4, 28, 56, 84, 112, 140]],
	[[28, 30, 30, 30], [37, 19, 60, 51], [4, 32, 60, 88, 116, 144]],
	[[28, 30, 30, 30], [38, 19, 63, 53], [4, 28, 52, 76, 100, 124, 148]],
	[[28, 30, 30, 30], [40, 20, 66, 56], [4, 22, 48, 74, 100, 126, 152]],
	[[28, 30, 30, 30], [43, 21, 70, 59], [4, 26, 52, 78, 104, 130, 156]],
	[[28, 30, 30, 30], [45, 22, 74, 62], [4, 30, 56, 82, 108, 134, 160]],
	[[28, 30, 30, 30], [47, 24, 77, 65], [4, 24, 52, 80, 108, 136, 164]],
	[[28, 30, 30, 30], [49, 25, 81, 68], [4, 28, 56, 84, 112, 140, 168]]];

// mode constants (cf. Table 2 in JIS X 0510:2004 p. 16)
var MODE_TERMINATOR = 0;
var MODE_NUMERIC = 1, MODE_ALPHANUMERIC = 2, MODE_OCTET = 4, MODE_KANJI = 8;

// validation regexps
var NUMERIC_REGEXP = /^\d*$/;
var ALPHANUMERIC_REGEXP = /^[A-Za-z0-9 $%*+\-./:]*$/;
var ALPHANUMERIC_OUT_REGEXP = /^[A-Z0-9 $%*+\-./:]*$/;

// ECC levels (cf. Table 22 in JIS X 0510:2004 p. 45)
var ECCLEVEL_L = 1, ECCLEVEL_M = 0, ECCLEVEL_Q = 3, ECCLEVEL_H = 2;

// GF(2^8)-to-integer mapping with a reducing polynomial x^8+x^4+x^3+x^2+1
// invariant: GF256_MAP[GF256_INVMAP[i]] == i for all i in [1,256)
var GF256_MAP = [], GF256_INVMAP = [-1];
for (var i = 0, v = 1; i < 255; ++i) {
	GF256_MAP.push(v);
	GF256_INVMAP[v] = i;
	v = (v * 2) ^ (v >= 128 ? 0x11d : 0);
}

// generator polynomials up to degree 30
// (should match with polynomials in JIS X 0510:2004 Appendix A)
//
// generator polynomial of degree K is product of (x-\alpha^0), (x-\alpha^1),
// ..., (x-\alpha^(K-1)). by convention, we omit the K-th coefficient (always 1)
// from the result; also other coefficients are written in terms of the exponent
// to \alpha to avoid the redundant calculation. (see also calculateecc below.)
var GF256_GENPOLY = [[]];
for (var i = 0; i < 30; ++i) {
	var prevpoly = GF256_GENPOLY[i], poly = [];
	for (var j = 0; j <= i; ++j) {
		var a = (j < i ? GF256_MAP[prevpoly[j]] : 0);
		var b = GF256_MAP[(i + (prevpoly[j - 1] || 0)) % 255];
		poly.push(GF256_INVMAP[a ^ b]);
	}
	GF256_GENPOLY.push(poly);
}

// alphanumeric character mapping (cf. Table 5 in JIS X 0510:2004 p. 19)
var ALPHANUMERIC_MAP = {};
for (var i = 0; i < 45; ++i) {
	ALPHANUMERIC_MAP['0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:'.charAt(i)] = i;
}

// mask functions in terms of row # and column #
// (cf. Table 20 in JIS X 0510:2004 p. 42)
/*jshint unused: false */
var MASKFUNCS = [
	function (i, j) {
		return (i + j) % 2 === 0;
	},
	function (i, j) {
		return i % 2 === 0;
	},
	function (i, j) {
		return j % 3 === 0;
	},
	function (i, j) {
		return (i + j) % 3 === 0;
	},
	function (i, j) {
		return (((i / 2) | 0) + ((j / 3) | 0)) % 2 === 0;
	},
	function (i, j) {
		return (i * j) % 2 + (i * j) % 3 === 0;
	},
	function (i, j) {
		return ((i * j) % 2 + (i * j) % 3) % 2 === 0;
	},
	function (i, j) {
		return ((i + j) % 2 + (i * j) % 3) % 2 === 0;
	}];

// returns true when the version information has to be embeded.
var needsverinfo = function (ver) {
	return ver > 6;
};

// returns the size of entire QR code for given version.
var getsizebyver = function (ver) {
	return 4 * ver + 17;
};

// returns the number of bits available for code words in this version.
var nfullbits = function (ver) {
	/*
	 * |<--------------- n --------------->|
	 * |        |<----- n-17 ---->|        |
	 * +-------+                ///+-------+ ----
	 * |       |                ///|       |    ^
	 * |  9x9  |       @@@@@    ///|  9x8  |    |
	 * |       | # # # @5x5@ # # # |       |    |
	 * +-------+       @@@@@       +-------+    |
	 *       #                               ---|
	 *                                        ^ |
	 *       #                                |
	 *     @@@@@       @@@@@       @@@@@      | n
	 *     @5x5@       @5x5@       @5x5@   n-17
	 *     @@@@@       @@@@@       @@@@@      | |
	 *       #                                | |
	 * //////                                 v |
	 * //////#                               ---|
	 * +-------+       @@@@@       @@@@@        |
	 * |       |       @5x5@       @5x5@        |
	 * |  8x9  |       @@@@@       @@@@@        |
	 * |       |                                v
	 * +-------+                             ----
	 *
	 * when the entire code has n^2 modules and there are m^2-3 alignment
	 * patterns, we have:
	 * - 225 (= 9x9 + 9x8 + 8x9) modules for finder patterns and
	 *   format information;
	 * - 2n-34 (= 2(n-17)) modules for timing patterns;
	 * - 36 (= 3x6 + 6x3) modules for version information, if any;
	 * - 25m^2-75 (= (m^2-3)(5x5)) modules for alignment patterns
	 *   if any, but 10m-20 (= 2(m-2)x5) of them overlaps with
	 *   timing patterns.
	 */
	var v = VERSIONS[ver];
	var nbits = 16 * ver * ver + 128 * ver + 64; // finder, timing and format info.
	if (needsverinfo(ver))
		nbits -= 36; // version information
	if (v[2].length) { // alignment patterns
		nbits -= 25 * v[2].length * v[2].length - 10 * v[2].length - 55;
	}
	return nbits;
};

// returns the number of bits available for data portions (i.e. excludes ECC
// bits but includes mode and length bits) in this version and ECC level.
var ndatabits = function (ver, ecclevel) {
	var nbits = nfullbits(ver) & ~7; // no sub-octet code words
	var v = VERSIONS[ver];
	nbits -= 8 * v[0][ecclevel] * v[1][ecclevel]; // ecc bits
	return nbits;
};

// returns the number of bits required for the length of data.
// (cf. Table 3 in JIS X 0510:2004 p. 16)
var ndatalenbits = function (ver, mode) {
	switch (mode) {
		case MODE_NUMERIC:
			return (ver < 10 ? 10 : ver < 27 ? 12 : 14);
		case MODE_ALPHANUMERIC:
			return (ver < 10 ? 9 : ver < 27 ? 11 : 13);
		case MODE_OCTET:
			return (ver < 10 ? 8 : 16);
		case MODE_KANJI:
			return (ver < 10 ? 8 : ver < 27 ? 10 : 12);
	}
};

// returns the maximum length of data possible in given configuration.
var getmaxdatalen = function (ver, mode, ecclevel) {
	var nbits = ndatabits(ver, ecclevel) - 4 - ndatalenbits(ver, mode); // 4 for mode bits
	switch (mode) {
		case MODE_NUMERIC:
			return ((nbits / 10) | 0) * 3 + (nbits % 10 < 4 ? 0 : nbits % 10 < 7 ? 1 : 2);
		case MODE_ALPHANUMERIC:
			return ((nbits / 11) | 0) * 2 + (nbits % 11 < 6 ? 0 : 1);
		case MODE_OCTET:
			return (nbits / 8) | 0;
		case MODE_KANJI:
			return (nbits / 13) | 0;
	}
};

// checks if the given data can be encoded in given mode, and returns
// the converted data for the further processing if possible. otherwise
// returns null.
//
// this function does not check the length of data; it is a duty of
// encode function below (as it depends on the version and ECC level too).
var validatedata = function (mode, data) {
	switch (mode) {
		case MODE_NUMERIC:
			if (!data.match(NUMERIC_REGEXP))
				return null;
			return data;

		case MODE_ALPHANUMERIC:
			if (!data.match(ALPHANUMERIC_REGEXP))
				return null;
			return data.toUpperCase();

		case MODE_OCTET:
			if (typeof data === 'string') { // encode as utf-8 string
				var newdata = [];
				for (var i = 0; i < data.length; ++i) {
					var ch = data.charCodeAt(i);
					if (ch < 0x80) {
						newdata.push(ch);
					} else if (ch < 0x800) {
						newdata.push(0xc0 | (ch >> 6),
							0x80 | (ch & 0x3f));
					} else if (ch < 0x10000) {
						newdata.push(0xe0 | (ch >> 12),
							0x80 | ((ch >> 6) & 0x3f),
							0x80 | (ch & 0x3f));
					} else {
						newdata.push(0xf0 | (ch >> 18),
							0x80 | ((ch >> 12) & 0x3f),
							0x80 | ((ch >> 6) & 0x3f),
							0x80 | (ch & 0x3f));
					}
				}
				return newdata;
			} else {
				return data;
			}
	}
};

// returns the code words (sans ECC bits) for given data and configurations.
// requires data to be preprocessed by validatedata. no length check is
// performed, and everything has to be checked before calling this function.
var encode = function (ver, mode, data, maxbuflen) {
	var buf = [];
	var bits = 0, remaining = 8;
	var datalen = data.length;

	// this function is intentionally no-op when n=0.
	var pack = function (x, n) {
		if (n >= remaining) {
			buf.push(bits | (x >> (n -= remaining)));
			while (n >= 8)
				buf.push((x >> (n -= 8)) & 255);
			bits = 0;
			remaining = 8;
		}
		if (n > 0)
			bits |= (x & ((1 << n) - 1)) << (remaining -= n);
	};

	var nlenbits = ndatalenbits(ver, mode);
	pack(mode, 4);
	pack(datalen, nlenbits);

	switch (mode) {
		case MODE_NUMERIC:
			for (var i = 2; i < datalen; i += 3) {
				pack(parseInt(data.substring(i - 2, i + 1), 10), 10);
			}
			pack(parseInt(data.substring(i - 2), 10), [0, 4, 7][datalen % 3]);
			break;

		case MODE_ALPHANUMERIC:
			for (var i = 1; i < datalen; i += 2) {
				pack(ALPHANUMERIC_MAP[data.charAt(i - 1)] * 45 +
					ALPHANUMERIC_MAP[data.charAt(i)], 11);
			}
			if (datalen % 2 == 1) {
				pack(ALPHANUMERIC_MAP[data.charAt(i - 1)], 6);
			}
			break;

		case MODE_OCTET:
			for (var i = 0; i < datalen; ++i) {
				pack(data[i], 8);
			}
			break;
	}

	// final bits. it is possible that adding terminator causes the buffer
	// to overflow, but then the buffer truncated to the maximum size will
	// be valid as the truncated terminator mode bits and padding is
	// identical in appearance (cf. JIS X 0510:2004 sec 8.4.8).
	pack(MODE_TERMINATOR, 4);
	if (remaining < 8)
		buf.push(bits);

	// the padding to fill up the remaining space. we should not add any
	// words when the overflow already occurred.
	while (buf.length + 1 < maxbuflen)
		buf.push(0xec, 0x11);
	if (buf.length < maxbuflen)
		buf.push(0xec);
	return buf;
};

// calculates ECC code words for given code words and generator polynomial.
//
// this is quite similar to CRC calculation as both Reed-Solomon and CRC use
// the certain kind of cyclic codes, which is effectively the division of
// zero-augumented polynomial by the generator polynomial. the only difference
// is that Reed-Solomon uses GF(2^8), instead of CRC's GF(2), and Reed-Solomon
// uses the different generator polynomial than CRC's.
var calculateecc = function (poly, genpoly) {
	var modulus = poly.slice(0);
	var polylen = poly.length, genpolylen = genpoly.length;
	for (var i = 0; i < genpolylen; ++i)
		modulus.push(0);
	for (var i = 0; i < polylen; ) {
		var quotient = GF256_INVMAP[modulus[i++]];
		if (quotient >= 0) {
			for (var j = 0; j < genpolylen; ++j) {
				modulus[i + j] ^= GF256_MAP[(quotient + genpoly[j]) % 255];
			}
		}
	}
	return modulus.slice(polylen);
};

// auguments ECC code words to given code words. the resulting words are
// ready to be encoded in the matrix.
//
// the much of actual augumenting procedure follows JIS X 0510:2004 sec 8.7.
// the code is simplified using the fact that the size of each code & ECC
// blocks is almost same; for example, when we have 4 blocks and 46 data words
// the number of code words in those blocks are 11, 11, 12, 12 respectively.
var augumenteccs = function (poly, nblocks, genpoly) {
	var subsizes = [];
	var subsize = (poly.length / nblocks) | 0, subsize0 = 0;
	var pivot = nblocks - poly.length % nblocks;
	for (var i = 0; i < pivot; ++i) {
		subsizes.push(subsize0);
		subsize0 += subsize;
	}
	for (var i = pivot; i < nblocks; ++i) {
		subsizes.push(subsize0);
		subsize0 += subsize + 1;
	}
	subsizes.push(subsize0);

	var eccs = [];
	for (var i = 0; i < nblocks; ++i) {
		eccs.push(calculateecc(poly.slice(subsizes[i], subsizes[i + 1]), genpoly));
	}

	var result = [];
	var nitemsperblock = (poly.length / nblocks) | 0;
	for (var i = 0; i < nitemsperblock; ++i) {
		for (var j = 0; j < nblocks; ++j) {
			result.push(poly[subsizes[j] + i]);
		}
	}
	for (var j = pivot; j < nblocks; ++j) {
		result.push(poly[subsizes[j + 1] - 1]);
	}
	for (var i = 0; i < genpoly.length; ++i) {
		for (var j = 0; j < nblocks; ++j) {
			result.push(eccs[j][i]);
		}
	}
	return result;
};

// auguments BCH(p+q,q) code to the polynomial over GF(2), given the proper
// genpoly. the both input and output are in binary numbers, and unlike
// calculateecc genpoly should include the 1 bit for the highest degree.
//
// actual polynomials used for this procedure are as follows:
// - p=10, q=5, genpoly=x^10+x^8+x^5+x^4+x^2+x+1 (JIS X 0510:2004 Appendix C)
// - p=18, q=6, genpoly=x^12+x^11+x^10+x^9+x^8+x^5+x^2+1 (ibid. Appendix D)
var augumentbch = function (poly, p, genpoly, q) {
	var modulus = poly << q;
	for (var i = p - 1; i >= 0; --i) {
		if ((modulus >> (q + i)) & 1)
			modulus ^= genpoly << i;
	}
	return (poly << q) | modulus;
};

// creates the base matrix for given version. it returns two matrices, one of
// them is the actual one and the another represents the "reserved" portion
// (e.g. finder and timing patterns) of the matrix.
//
// some entries in the matrix may be undefined, rather than 0 or 1. this is
// intentional (no initialization needed!), and putdata below will fill
// the remaining ones.
var makebasematrix = function (ver) {
	var v = VERSIONS[ver], n = getsizebyver(ver);
	var matrix = [], reserved = [];
	for (var i = 0; i < n; ++i) {
		matrix.push([]);
		reserved.push([]);
	}

	var blit = function (y, x, h, w, bits) {
		for (var i = 0; i < h; ++i) {
			for (var j = 0; j < w; ++j) {
				matrix[y + i][x + j] = (bits[i] >> j) & 1;
				reserved[y + i][x + j] = 1;
			}
		}
	};

	// finder patterns and a part of timing patterns
	// will also mark the format information area (not yet written) as reserved.
	blit(0, 0, 9, 9, [0x7f, 0x41, 0x5d, 0x5d, 0x5d, 0x41, 0x17f, 0x00, 0x40]);
	blit(n - 8, 0, 8, 9, [0x100, 0x7f, 0x41, 0x5d, 0x5d, 0x5d, 0x41, 0x7f]);
	blit(0, n - 8, 9, 8, [0xfe, 0x82, 0xba, 0xba, 0xba, 0x82, 0xfe, 0x00, 0x00]);

	// the rest of timing patterns
	for (var i = 9; i < n - 8; ++i) {
		matrix[6][i] = matrix[i][6] = ~i & 1;
		reserved[6][i] = reserved[i][6] = 1;
	}

	// alignment patterns
	var aligns = v[2], m = aligns.length;
	for (var i = 0; i < m; ++i) {
		var minj = (i === 0 || i === m - 1 ? 1 : 0), maxj = (i === 0 ? m - 1 : m);
		for (var j = minj; j < maxj; ++j) {
			blit(aligns[i], aligns[j], 5, 5, [0x1f, 0x11, 0x15, 0x11, 0x1f]);
		}
	}

	// version information
	if (needsverinfo(ver)) {
		var code = augumentbch(ver, 6, 0x1f25, 12);
		var k = 0;
		for (var i = 0; i < 6; ++i) {
			for (var j = 0; j < 3; ++j) {
				matrix[i][(n - 11) + j] = matrix[(n - 11) + j][i] = (code >> k++) & 1;
				reserved[i][(n - 11) + j] = reserved[(n - 11) + j][i] = 1;
			}
		}
	}

	return {matrix: matrix, reserved: reserved};
};

// fills the data portion (i.e. unmarked in reserved) of the matrix with given
// code words. the size of code words should be no more than available bits,
// and remaining bits are padded to 0 (cf. JIS X 0510:2004 sec 8.7.3).
var putdata = function (matrix, reserved, buf) {
	var n = matrix.length;
	var k = 0, dir = -1;
	for (var i = n - 1; i >= 0; i -= 2) {
		if (i == 6)
			--i; // skip the entire timing pattern column
		var jj = (dir < 0 ? n - 1 : 0);
		for (var j = 0; j < n; ++j) {
			for (var ii = i; ii > i - 2; --ii) {
				if (!reserved[jj][ii]) {
					// may overflow, but (undefined >> x)
					// is 0 so it will auto-pad to zero.
					matrix[jj][ii] = (buf[k >> 3] >> (~k & 7)) & 1;
					++k;
				}
			}
			jj += dir;
		}
		dir = -dir;
	}
	return matrix;
};

// XOR-masks the data portion of the matrix. repeating the call with the same
// arguments will revert the prior call (convenient in the matrix evaluation).
var maskdata = function (matrix, reserved, mask) {
	var maskf = MASKFUNCS[mask];
	var n = matrix.length;
	for (var i = 0; i < n; ++i) {
		for (var j = 0; j < n; ++j) {
			if (!reserved[i][j])
				matrix[i][j] ^= maskf(i, j);
		}
	}
	return matrix;
};

// puts the format information.
var putformatinfo = function (matrix, reserved, ecclevel, mask) {
	var n = matrix.length;
	var code = augumentbch((ecclevel << 3) | mask, 5, 0x537, 10) ^ 0x5412;
	for (var i = 0; i < 15; ++i) {
		var r = [0, 1, 2, 3, 4, 5, 7, 8, n - 7, n - 6, n - 5, n - 4, n - 3, n - 2, n - 1][i];
		var c = [n - 1, n - 2, n - 3, n - 4, n - 5, n - 6, n - 7, n - 8, 7, 5, 4, 3, 2, 1, 0][i];
		matrix[r][8] = matrix[8][c] = (code >> i) & 1;
		// we don't have to mark those bits reserved; always done
		// in makebasematrix above.
	}
	return matrix;
};

// evaluates the resulting matrix and returns the score (lower is better).
// (cf. JIS X 0510:2004 sec 8.8.2)
//
// the evaluation procedure tries to avoid the problematic patterns naturally
// occuring from the original matrix. for example, it penaltizes the patterns
// which just look like the finder pattern which will confuse the decoder.
// we choose the mask which results in the lowest score among 8 possible ones.
//
// note: zxing seems to use the same procedure and in many cases its choice
// agrees to ours, but sometimes it does not. practically it doesn't matter.
var evaluatematrix = function (matrix) {
	// N1+(k-5) points for each consecutive row of k same-colored modules,
	// where k >= 5. no overlapping row counts.
	var PENALTY_CONSECUTIVE = 3;
	// N2 points for each 2x2 block of same-colored modules.
	// overlapping block does count.
	var PENALTY_TWOBYTWO = 3;
	// N3 points for each pattern with >4W:1B:1W:3B:1W:1B or
	// 1B:1W:3B:1W:1B:>4W, or their multiples (e.g. highly unlikely,
	// but 13W:3B:3W:9B:3W:3B counts).
	var PENALTY_FINDERLIKE = 40;
	// N4*k points for every (5*k)% deviation from 50% black density.
	// i.e. k=1 for 55~60% and 40~45%, k=2 for 60~65% and 35~40%, etc.
	var PENALTY_DENSITY = 10;

	var evaluategroup = function (groups) { // assumes [W,B,W,B,W,...,B,W]
		var score = 0;
		for (var i = 0; i < groups.length; ++i) {
			if (groups[i] >= 5)
				score += PENALTY_CONSECUTIVE + (groups[i] - 5);
		}
		for (var i = 5; i < groups.length; i += 2) {
			var p = groups[i];
			if (groups[i - 1] == p && groups[i - 2] == 3 * p && groups[i - 3] == p &&
				groups[i - 4] == p && (groups[i - 5] >= 4 * p || groups[i + 1] >= 4 * p)) {
				// this part differs from zxing...
				score += PENALTY_FINDERLIKE;
			}
		}
		return score;
	};

	var n = matrix.length;
	var score = 0, nblacks = 0;
	for (var i = 0; i < n; ++i) {
		var row = matrix[i];
		var groups;

		// evaluate the current row
		groups = [0]; // the first empty group of white
		for (var j = 0; j < n; ) {
			var k;
			for (k = 0; j < n && row[j]; ++k)
				++j;
			groups.push(k);
			for (k = 0; j < n && !row[j]; ++k)
				++j;
			groups.push(k);
		}
		score += evaluategroup(groups);

		// evaluate the current column
		groups = [0];
		for (var j = 0; j < n; ) {
			var k;
			for (k = 0; j < n && matrix[j][i]; ++k)
				++j;
			groups.push(k);
			for (k = 0; j < n && !matrix[j][i]; ++k)
				++j;
			groups.push(k);
		}
		score += evaluategroup(groups);

		// check the 2x2 box and calculate the density
		var nextrow = matrix[i + 1] || [];
		nblacks += row[0];
		for (var j = 1; j < n; ++j) {
			var p = row[j];
			nblacks += p;
			// at least comparison with next row should be strict...
			if (row[j - 1] == p && nextrow[j] === p && nextrow[j - 1] === p) {
				score += PENALTY_TWOBYTWO;
			}
		}
	}

	score += PENALTY_DENSITY * ((Math.abs(nblacks / n / n - 0.5) / 0.05) | 0);
	return score;
};

// returns the fully encoded QR code matrix which contains given data.
// it also chooses the best mask automatically when mask is -1.
var generate = function (data, ver, mode, ecclevel, mask) {
	var v = VERSIONS[ver];
	var buf = encode(ver, mode, data, ndatabits(ver, ecclevel) >> 3);
	buf = augumenteccs(buf, v[1][ecclevel], GF256_GENPOLY[v[0][ecclevel]]);

	var result = makebasematrix(ver);
	var matrix = result.matrix, reserved = result.reserved;
	putdata(matrix, reserved, buf);

	if (mask < 0) {
		// find the best mask
		maskdata(matrix, reserved, 0);
		putformatinfo(matrix, reserved, ecclevel, 0);
		var bestmask = 0, bestscore = evaluatematrix(matrix);
		maskdata(matrix, reserved, 0);
		for (mask = 1; mask < 8; ++mask) {
			maskdata(matrix, reserved, mask);
			putformatinfo(matrix, reserved, ecclevel, mask);
			var score = evaluatematrix(matrix);
			if (bestscore > score) {
				bestscore = score;
				bestmask = mask;
			}
			maskdata(matrix, reserved, mask);
		}
		mask = bestmask;
	}

	maskdata(matrix, reserved, mask);
	putformatinfo(matrix, reserved, ecclevel, mask);
	return matrix;
};

// the public interface is trivial; the options available are as follows:
//
// - version: an integer in [1,40]. when omitted (or -1) the smallest possible
//   version is chosen.
// - mode: one of 'numeric', 'alphanumeric', 'octet'. when omitted the smallest
//   possible mode is chosen.
// - eccLevel: one of 'L', 'M', 'Q', 'H'. defaults to 'L'.
// - mask: an integer in [0,7]. when omitted (or -1) the best mask is chosen.
//

function generateFrame(data, options) {
	var MODES = {'numeric': MODE_NUMERIC, 'alphanumeric': MODE_ALPHANUMERIC,
		'octet': MODE_OCTET};
	var ECCLEVELS = {'L': ECCLEVEL_L, 'M': ECCLEVEL_M, 'Q': ECCLEVEL_Q,
		'H': ECCLEVEL_H};

	options = options || {};
	var ver = options.version || -1;
	var ecclevel = ECCLEVELS[(options.eccLevel || 'L').toUpperCase()];
	var mode = options.mode ? MODES[options.mode.toLowerCase()] : -1;
	var mask = 'mask' in options ? options.mask : -1;

	if (mode < 0) {
		if (typeof data === 'string') {
			if (data.match(NUMERIC_REGEXP)) {
				mode = MODE_NUMERIC;
			} else if (data.match(ALPHANUMERIC_OUT_REGEXP)) {
				// while encode supports case-insensitive encoding, we restrict the data to be uppercased when auto-selecting the mode.
				mode = MODE_ALPHANUMERIC;
			} else {
				mode = MODE_OCTET;
			}
		} else {
			mode = MODE_OCTET;
		}
	} else if (!(mode == MODE_NUMERIC || mode == MODE_ALPHANUMERIC ||
		mode == MODE_OCTET)) {
		throw 'invalid or unsupported mode';
	}

	data = validatedata(mode, data);
	if (data === null)
		throw 'invalid data format';

	if (ecclevel < 0 || ecclevel > 3)
		throw 'invalid ECC level';

	if (ver < 0) {
		for (ver = 1; ver <= 40; ++ver) {
			if (data.length <= getmaxdatalen(ver, mode, ecclevel))
				break;
		}
		if (ver > 40)
			throw 'too large data for the Qr format';
	} else if (ver < 1 || ver > 40) {
		throw 'invalid Qr version! should be between 1 and 40';
	}

	if (mask != -1 && (mask < 0 || mask > 8))
		throw 'invalid mask';
	//console.log('version:', ver, 'mode:', mode, 'ECC:', ecclevel, 'mask:', mask )
	return generate(data, ver, mode, ecclevel, mask);
}


// options
// - modulesize: a number. this is a size of each modules in pixels, and
//   defaults to 5px.
// - margin: a number. this is a size of margin in *modules*, and defaults to
//   4 (white modules). the specficiation mandates the margin no less than 4
//   modules, so it is better not to alter this value unless you know what
//   you're doing.
function buildCanvas(data, options) {

	var canvas = [];
	var background = options.background || '#fff';
	var foreground = options.foreground || '#000';
	var padding = options.padding || 0;
	//var margin = options.margin || 4;
	var matrix = generateFrame(data, options);
	var n = matrix.length;
	var modSize = Math.floor(options.fit ? options.fit / n : 5);
	var size = (n * modSize) + (modSize * padding * 2);
	var paddingXY = modSize * padding;

	canvas.push({
		type: 'rect',
		x: 0, y: 0, w: size, h: size, lineWidth: 0, color: background
	});

	for (var i = 0; i < n; ++i) {
		for (var j = 0; j < n; ++j) {
			if (matrix[i][j]) {
				canvas.push({
					type: 'rect',
					x: modSize * j + paddingXY,
					y: modSize * i + paddingXY,
					w: modSize,
					h: modSize,
					lineWidth: 0,
					color: foreground
				});
			}
		}
	}

	return {
		canvas: canvas,
		size: size
	};

}

function measure(node) {
	var cd = buildCanvas(node.qr, node);
	node._canvas = cd.canvas;
	node._width = node._height = node._minWidth = node._maxWidth = node._minHeight = node._maxHeight = cd.size;
	return node;
}

module.exports = {
	measure: measure
};

/***/ }),

/***/ 70098:
/***/ (function(module) {

"use strict";


module.exports = {
	'4A0': [4767.87, 6740.79],
	'2A0': [3370.39, 4767.87],
	A0: [2383.94, 3370.39],
	A1: [1683.78, 2383.94],
	A2: [1190.55, 1683.78],
	A3: [841.89, 1190.55],
	A4: [595.28, 841.89],
	A5: [419.53, 595.28],
	A6: [297.64, 419.53],
	A7: [209.76, 297.64],
	A8: [147.40, 209.76],
	A9: [104.88, 147.40],
	A10: [73.70, 104.88],
	B0: [2834.65, 4008.19],
	B1: [2004.09, 2834.65],
	B2: [1417.32, 2004.09],
	B3: [1000.63, 1417.32],
	B4: [708.66, 1000.63],
	B5: [498.90, 708.66],
	B6: [354.33, 498.90],
	B7: [249.45, 354.33],
	B8: [175.75, 249.45],
	B9: [124.72, 175.75],
	B10: [87.87, 124.72],
	C0: [2599.37, 3676.54],
	C1: [1836.85, 2599.37],
	C2: [1298.27, 1836.85],
	C3: [918.43, 1298.27],
	C4: [649.13, 918.43],
	C5: [459.21, 649.13],
	C6: [323.15, 459.21],
	C7: [229.61, 323.15],
	C8: [161.57, 229.61],
	C9: [113.39, 161.57],
	C10: [79.37, 113.39],
	RA0: [2437.80, 3458.27],
	RA1: [1729.13, 2437.80],
	RA2: [1218.90, 1729.13],
	RA3: [864.57, 1218.90],
	RA4: [609.45, 864.57],
	SRA0: [2551.18, 3628.35],
	SRA1: [1814.17, 2551.18],
	SRA2: [1275.59, 1814.17],
	SRA3: [907.09, 1275.59],
	SRA4: [637.80, 907.09],
	EXECUTIVE: [521.86, 756.00],
	FOLIO: [612.00, 936.00],
	LEGAL: [612.00, 1008.00],
	LETTER: [612.00, 792.00],
	TABLOID: [792.00, 1224.00]
};


/***/ }),

/***/ 76442:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isString = (__webpack_require__(91867).isString);
var isArray = (__webpack_require__(91867).isArray);
var isUndefined = (__webpack_require__(91867).isUndefined);
var isNull = (__webpack_require__(91867).isNull);

/**
 * Creates an instance of StyleContextStack used for style inheritance and style overrides
 *
 * @constructor
 * @this {StyleContextStack}
 * @param {Object} named styles dictionary
 * @param {Object} optional default style definition
 */
function StyleContextStack(styleDictionary, defaultStyle) {
	this.defaultStyle = defaultStyle || {};
	this.styleDictionary = styleDictionary;
	this.styleOverrides = [];
}

/**
 * Creates cloned version of current stack
 * @return {StyleContextStack} current stack snapshot
 */
StyleContextStack.prototype.clone = function () {
	var stack = new StyleContextStack(this.styleDictionary, this.defaultStyle);

	this.styleOverrides.forEach(function (item) {
		stack.styleOverrides.push(item);
	});

	return stack;
};

/**
 * Pushes style-name or style-overrides-object onto the stack for future evaluation
 *
 * @param {String|Object} styleNameOrOverride style-name (referring to styleDictionary) or
 *                                            a new dictionary defining overriding properties
 */
StyleContextStack.prototype.push = function (styleNameOrOverride) {
	this.styleOverrides.push(styleNameOrOverride);
};

/**
 * Removes last style-name or style-overrides-object from the stack
 *
 * @param {Number} howMany - optional number of elements to be popped (if not specified,
 *                           one element will be removed from the stack)
 */
StyleContextStack.prototype.pop = function (howMany) {
	howMany = howMany || 1;

	while (howMany-- > 0) {
		this.styleOverrides.pop();
	}
};

/**
 * Creates a set of named styles or/and a style-overrides-object based on the item,
 * pushes those elements onto the stack for future evaluation and returns the number
 * of elements pushed, so they can be easily poped then.
 *
 * @param {Object} item - an object with optional style property and/or style overrides
 * @return the number of items pushed onto the stack
 */
StyleContextStack.prototype.autopush = function (item) {
	if (isString(item)) {
		return 0;
	}

	var styleNames = [];

	if (item.style) {
		if (isArray(item.style)) {
			styleNames = item.style;
		} else {
			styleNames = [item.style];
		}
	}

	for (var i = 0, l = styleNames.length; i < l; i++) {
		this.push(styleNames[i]);
	}

	// rather than spend significant time making a styleOverrideObject, just add item
	this.push(item);
	return styleNames.length + 1;
};

/**
 * Automatically pushes elements onto the stack, using autopush based on item,
 * executes callback and then pops elements back. Returns value returned by callback
 *
 * @param  {Object}   item - an object with optional style property and/or style overrides
 * @param  {Function} function to be called between autopush and pop
 * @return {Object} value returned by callback
 */
StyleContextStack.prototype.auto = function (item, callback) {
	var pushedItems = this.autopush(item);
	var result = callback();

	if (pushedItems > 0) {
		this.pop(pushedItems);
	}

	return result;
};

/**
 * Evaluates stack and returns value of a named property
 *
 * @param {String} property - property name
 * @return property value or null if not found
 */
StyleContextStack.prototype.getProperty = function (property) {
	if (this.styleOverrides) {
		for (var i = this.styleOverrides.length - 1; i >= 0; i--) {
			var item = this.styleOverrides[i];

			if (isString(item)) {
				// named-style-override
				var style = this.styleDictionary[item];
				if (style && !isUndefined(style[property]) && !isNull(style[property])) {
					return style[property];
				}
			} else if (!isUndefined(item[property]) && !isNull(item[property])) {
				// style-overrides-object
				return item[property];
			}
		}
	}

	return this.defaultStyle && this.defaultStyle[property];
};

module.exports = StyleContextStack;


/***/ }),

/***/ 89638:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var xmldoc = __webpack_require__(52242);

/** Strip unit postfix, parse number, but return undefined instead of NaN for bad input */
function stripUnits(textVal) {
	var n = parseFloat(textVal);
	if (typeof n !== 'number' || isNaN(n)) {
		return undefined;
	}
	return n;
}

/** Make sure it's valid XML and the root tage is <svg/>, returns xmldoc DOM */
function parseSVG(svgString) {
	var doc;

	try {
		doc = new xmldoc.XmlDocument(svgString);
	} catch (err) {
		throw new Error('SVGMeasure: ' + err);
	}

	if (doc.name !== "svg") {
		throw new Error('SVGMeasure: expected <svg> document');
	}

	return doc;
}

function SVGMeasure() {
}

SVGMeasure.prototype.measureSVG = function (svgString) {

	var doc = parseSVG(svgString);

	var docWidth = stripUnits(doc.attr.width);
	var docHeight = stripUnits(doc.attr.height);

	if ((docWidth == undefined || docHeight == undefined) && typeof doc.attr.viewBox == 'string') {
		var viewBoxParts = doc.attr.viewBox.split(/[,\s]+/);
		if (viewBoxParts.length !== 4) {
			throw new Error("Unexpected svg viewbox format, should have 4 entries but found: '" + doc.attr.viewBox + "'");
		}
		if (docWidth == undefined) {
			docWidth = stripUnits(viewBoxParts[2]);
		}
		if (docHeight == undefined) {
			docHeight = stripUnits(viewBoxParts[3]);
		}
	}

	return {
		width: docWidth,
		height: docHeight
	};
};

SVGMeasure.prototype.writeDimensions = function (svgString, dimensions) {

	var doc = parseSVG(svgString);

	doc.attr.width = "" + dimensions.width;
	doc.attr.height = "" + dimensions.height;

	return doc.toString();
};

module.exports = SVGMeasure;


/***/ }),

/***/ 89836:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var ColumnCalculator = __webpack_require__(77530);
var isFunction = (__webpack_require__(91867).isFunction);
var isNumber = (__webpack_require__(91867).isNumber);
var isPositiveInteger = (__webpack_require__(91867).isPositiveInteger);

function TableProcessor(tableNode) {
	this.tableNode = tableNode;
}

TableProcessor.prototype.beginTable = function (writer) {
	var tableNode;
	var availableWidth;
	var self = this;

	tableNode = this.tableNode;
	this.offsets = tableNode._offsets;
	this.layout = tableNode._layout;

	availableWidth = writer.context().availableWidth - this.offsets.total;
	ColumnCalculator.buildColumnWidths(tableNode.table.widths, availableWidth, this.offsets.total, tableNode);

	this.tableWidth = tableNode._offsets.total + getTableInnerContentWidth();
	this.rowSpanData = prepareRowSpanData();
	this.cleanUpRepeatables = false;

	// headersRows and rowsWithoutPageBreak (headerRows + keepWithHeaderRows)
	this.headerRows = 0;
	this.rowsWithoutPageBreak = 0;

	var headerRows = tableNode.table.headerRows;

	if (isPositiveInteger(headerRows)) {
		this.headerRows = headerRows;

		if (this.headerRows > tableNode.table.body.length) {
			throw new Error(`Too few rows in the table. Property headerRows requires at least ${this.headerRows}, contains only ${tableNode.table.body.length}`);
		}

		this.rowsWithoutPageBreak = this.headerRows;

		const keepWithHeaderRows = tableNode.table.keepWithHeaderRows;

		if (isPositiveInteger(keepWithHeaderRows)) {
			this.rowsWithoutPageBreak += keepWithHeaderRows;
		}
	}

	this.dontBreakRows = tableNode.table.dontBreakRows || false;

	if (this.rowsWithoutPageBreak || this.dontBreakRows) {
		writer.beginUnbreakableBlock();
		// Draw the top border of the table
		this.drawHorizontalLine(0, writer);
		if (this.rowsWithoutPageBreak && this.dontBreakRows) {
			// We just increase the value of transactionLevel
			writer.beginUnbreakableBlock();
		}
	}

	// update the border properties of all cells before drawing any lines
	prepareCellBorders(this.tableNode.table.body);

	function getTableInnerContentWidth() {
		var width = 0;

		tableNode.table.widths.forEach(function (w) {
			width += w._calcWidth;
		});

		return width;
	}

	function prepareRowSpanData() {
		var rsd = [];
		var x = 0;
		var lastWidth = 0;

		rsd.push({ left: 0, rowSpan: 0 });

		for (var i = 0, l = self.tableNode.table.body[0].length; i < l; i++) {
			var paddings = self.layout.paddingLeft(i, self.tableNode) + self.layout.paddingRight(i, self.tableNode);
			var lBorder = self.layout.vLineWidth(i, self.tableNode);
			lastWidth = paddings + lBorder + self.tableNode.table.widths[i]._calcWidth;
			rsd[rsd.length - 1].width = lastWidth;
			x += lastWidth;
			rsd.push({ left: x, rowSpan: 0, width: 0 });
		}

		return rsd;
	}

	// Iterate through all cells. If the current cell is the start of a
	// rowSpan/colSpan, update the border property of the cells on its
	// bottom/right accordingly. This is needed since each iteration of the
	// line-drawing loops draws lines for a single cell, not for an entire
	// rowSpan/colSpan.
	function prepareCellBorders(body) {
		for (var rowIndex = 0; rowIndex < body.length; rowIndex++) {
			var row = body[rowIndex];

			for (var colIndex = 0; colIndex < row.length; colIndex++) {
				var cell = row[colIndex];

				if (cell.border) {
					var rowSpan = cell.rowSpan || 1;
					var colSpan = cell.colSpan || 1;

					for (var rowOffset = 0; rowOffset < rowSpan; rowOffset++) {
						// set left border
						if (cell.border[0] !== undefined && rowOffset > 0) {
							setBorder(rowIndex + rowOffset, colIndex, 0, cell.border[0]);
						}

						// set right border
						if (cell.border[2] !== undefined) {
							setBorder(rowIndex + rowOffset, colIndex + colSpan - 1, 2, cell.border[2]);
						}
					}

					for (var colOffset = 0; colOffset < colSpan; colOffset++) {
						// set top border
						if (cell.border[1] !== undefined && colOffset > 0) {
							setBorder(rowIndex, colIndex + colOffset, 1, cell.border[1]);
						}

						// set bottom border
						if (cell.border[3] !== undefined) {
							setBorder(rowIndex + rowSpan - 1, colIndex + colOffset, 3, cell.border[3]);
						}
					}
				}
			}
		}

		// helper function to set the border for a given cell
		function setBorder(rowIndex, colIndex, borderIndex, borderValue) {
			var cell = body[rowIndex][colIndex];
			cell.border = cell.border || {};
			cell.border[borderIndex] = borderValue;
		}
	}
};

TableProcessor.prototype.onRowBreak = function (rowIndex, writer) {
	var self = this;
	return function () {
		var offset = self.rowPaddingTop + (!self.headerRows ? self.topLineWidth : 0);
		writer.context().availableHeight -= self.reservedAtBottom;
		writer.context().moveDown(offset);
	};
};

TableProcessor.prototype.beginRow = function (rowIndex, writer) {
	this.topLineWidth = this.layout.hLineWidth(rowIndex, this.tableNode);
	this.rowPaddingTop = this.layout.paddingTop(rowIndex, this.tableNode);
	this.bottomLineWidth = this.layout.hLineWidth(rowIndex + 1, this.tableNode);
	this.rowPaddingBottom = this.layout.paddingBottom(rowIndex, this.tableNode);

	this.rowCallback = this.onRowBreak(rowIndex, writer);
	writer.tracker.startTracking('pageChanged', this.rowCallback);
	if (rowIndex == 0 && !this.dontBreakRows && !this.rowsWithoutPageBreak) {
		// We store the 'y' to draw later and if necessary the top border of the table
		this._tableTopBorderY = writer.context().y;
		writer.context().moveDown(this.topLineWidth);
	}
	if (this.dontBreakRows && rowIndex > 0) {
		writer.beginUnbreakableBlock();
	}
	this.rowTopY = writer.context().y;
	this.reservedAtBottom = this.bottomLineWidth + this.rowPaddingBottom;

	writer.context().availableHeight -= this.reservedAtBottom;

	writer.context().moveDown(this.rowPaddingTop);
};

TableProcessor.prototype.drawHorizontalLine = function (lineIndex, writer, overrideY, moveDown = true, forcePage) {
	var lineWidth = this.layout.hLineWidth(lineIndex, this.tableNode);
	if (lineWidth) {
		var style = this.layout.hLineStyle(lineIndex, this.tableNode);
		var dash;
		if (style && style.dash) {
			dash = style.dash;
		}

		var offset = lineWidth / 2;
		var currentLine = null;
		var body = this.tableNode.table.body;
		var cellAbove;
		var currentCell;
		var rowCellAbove;

		for (var i = 0, l = this.rowSpanData.length; i < l; i++) {
			var data = this.rowSpanData[i];
			var shouldDrawLine = !data.rowSpan;
			var borderColor = null;

			// draw only if the current cell requires a top border or the cell in the
			// row above requires a bottom border
			if (shouldDrawLine && i < l - 1) {
				var topBorder = false, bottomBorder = false, rowBottomBorder = false;

				// the cell in the row above
				if (lineIndex > 0) {
					cellAbove = body[lineIndex - 1][i];
					bottomBorder = cellAbove.border ? cellAbove.border[3] : this.layout.defaultBorder;
					if (bottomBorder && cellAbove.borderColor) {
						borderColor = cellAbove.borderColor[3];
					}
				}

				// the current cell
				if (lineIndex < body.length) {
					currentCell = body[lineIndex][i];
					topBorder = currentCell.border ? currentCell.border[1] : this.layout.defaultBorder;
					if (topBorder && borderColor == null && currentCell.borderColor) {
						borderColor = currentCell.borderColor[1];
					}
				}

				shouldDrawLine = topBorder || bottomBorder;
			}

			if (cellAbove && cellAbove._rowSpanCurrentOffset) {
				rowCellAbove = body[lineIndex - 1 - cellAbove._rowSpanCurrentOffset][i];
				rowBottomBorder = rowCellAbove && rowCellAbove.border ? rowCellAbove.border[3] : this.layout.defaultBorder;
				if (rowBottomBorder && rowCellAbove && rowCellAbove.borderColor) {
					borderColor = rowCellAbove.borderColor[3];
				}
			}

			if (borderColor == null) {
				borderColor = isFunction(this.layout.hLineColor) ? this.layout.hLineColor(lineIndex, this.tableNode, i) : this.layout.hLineColor;
			}

			if (!currentLine && shouldDrawLine) {
				currentLine = { left: data.left, width: 0 };
			}

			if (shouldDrawLine) {
				var colSpanIndex = 0;
				if (rowCellAbove && rowCellAbove.colSpan && rowBottomBorder) {
					while (rowCellAbove.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else if (cellAbove && cellAbove.colSpan && bottomBorder) {
					while (cellAbove.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else if (currentCell && currentCell.colSpan && topBorder) {
					while (currentCell.colSpan > colSpanIndex) {
						currentLine.width += (this.rowSpanData[i + colSpanIndex++].width || 0);
					}
					i += colSpanIndex - 1;
				} else {
					currentLine.width += (this.rowSpanData[i].width || 0);
				}
			}

			var y = (overrideY || 0) + offset;


			if (shouldDrawLine) {
				if (currentLine && currentLine.width) {
					writer.addVector({
						type: 'line',
						x1: currentLine.left,
						x2: currentLine.left + currentLine.width,
						y1: y,
						y2: y,
						lineWidth: lineWidth,
						dash: dash,
						lineColor: borderColor
					}, false, isNumber(overrideY), null, forcePage);
					currentLine = null;
					borderColor = null;
					cellAbove = null;
					currentCell = null;
					rowCellAbove = null;
				}
			}
		}

		if (moveDown) {
			writer.context().moveDown(lineWidth);
		}
	}
};

TableProcessor.prototype.drawVerticalLine = function (x, y0, y1, vLineColIndex, writer, vLineRowIndex, beforeVLineColIndex) {
	var width = this.layout.vLineWidth(vLineColIndex, this.tableNode);
	if (width === 0) {
		return;
	}
	var style = this.layout.vLineStyle(vLineColIndex, this.tableNode);
	var dash;
	if (style && style.dash) {
		dash = style.dash;
	}

	var body = this.tableNode.table.body;
	var cellBefore;
	var currentCell;
	var borderColor;

	// the cell in the col before
	if (vLineColIndex > 0) {
		cellBefore = body[vLineRowIndex][beforeVLineColIndex];
		if (cellBefore && cellBefore.borderColor) {
			if (cellBefore.border ? cellBefore.border[2] : this.layout.defaultBorder) {
				borderColor = cellBefore.borderColor[2];
			}
		}
	}

	// the current cell
	if (borderColor == null && vLineColIndex < body.length) {
		currentCell = body[vLineRowIndex][vLineColIndex];
		if (currentCell && currentCell.borderColor) {
			if (currentCell.border ? currentCell.border[0] : this.layout.defaultBorder) {
				borderColor = currentCell.borderColor[0];
			}
		}
	}

	if (borderColor == null && cellBefore && cellBefore._rowSpanCurrentOffset) {
		var rowCellBeforeAbove = body[vLineRowIndex - cellBefore._rowSpanCurrentOffset][beforeVLineColIndex];
		if (rowCellBeforeAbove.borderColor) {
			if (rowCellBeforeAbove.border ? rowCellBeforeAbove.border[2] : this.layout.defaultBorder) {
				borderColor = rowCellBeforeAbove.borderColor[2];
			}
		}
	}

	if (borderColor == null && currentCell && currentCell._rowSpanCurrentOffset) {
		var rowCurrentCellAbove = body[vLineRowIndex - currentCell._rowSpanCurrentOffset][vLineColIndex];
		if (rowCurrentCellAbove.borderColor) {
			if (rowCurrentCellAbove.border ? rowCurrentCellAbove.border[2] : this.layout.defaultBorder) {
				borderColor = rowCurrentCellAbove.borderColor[2];
			}
		}
	}

	if (borderColor == null) {
		borderColor = isFunction(this.layout.vLineColor) ? this.layout.vLineColor(vLineColIndex, this.tableNode, vLineRowIndex) : this.layout.vLineColor;
	}
	writer.addVector({
		type: 'line',
		x1: x + width / 2,
		x2: x + width / 2,
		y1: y0,
		y2: y1,
		lineWidth: width,
		dash: dash,
		lineColor: borderColor
	}, false, true);
	cellBefore = null;
	currentCell = null;
	borderColor = null;
};

TableProcessor.prototype.endTable = function (writer) {
	if (this.cleanUpRepeatables) {
		writer.popFromRepeatables();
	}
};

TableProcessor.prototype.endRow = function (rowIndex, writer, pageBreaks) {
	var l, i;
	var self = this;
	writer.tracker.stopTracking('pageChanged', this.rowCallback);
	writer.context().moveDown(this.layout.paddingBottom(rowIndex, this.tableNode));
	writer.context().availableHeight += this.reservedAtBottom;

	var endingPage = writer.context().page;
	var endingY = writer.context().y;

	var xs = getLineXs();

	var ys = [];

	var hasBreaks = pageBreaks && pageBreaks.length > 0;
	var body = this.tableNode.table.body;

	ys.push({
		y0: this.rowTopY,
		page: hasBreaks ? pageBreaks[0].prevPage : endingPage
	});

	if (hasBreaks) {
		for (i = 0, l = pageBreaks.length; i < l; i++) {
			var pageBreak = pageBreaks[i];
			ys[ys.length - 1].y1 = pageBreak.prevY;

			ys.push({ y0: pageBreak.y, page: pageBreak.prevPage + 1 });
		}
	}

	ys[ys.length - 1].y1 = endingY;

	var skipOrphanePadding = (ys[0].y1 - ys[0].y0 === this.rowPaddingTop);
	if (rowIndex === 0 && !skipOrphanePadding && !this.rowsWithoutPageBreak && !this.dontBreakRows) {
		// Draw the top border of the table
		var pageTableStartedAt = null;
		if (pageBreaks && pageBreaks.length > 0) {
			// Get the page where table started at
			pageTableStartedAt = pageBreaks[0].prevPage;
		}
		this.drawHorizontalLine(0, writer, this._tableTopBorderY, false, pageTableStartedAt);
	}
	for (var yi = (skipOrphanePadding ? 1 : 0), yl = ys.length; yi < yl; yi++) {
		var willBreak = yi < ys.length - 1;
		var rowBreakWithoutHeader = (yi > 0 && !this.headerRows);
		var hzLineOffset = rowBreakWithoutHeader ? 0 : this.topLineWidth;
		var y1 = ys[yi].y0;
		var y2 = ys[yi].y1;

		if (willBreak) {
			y2 = y2 + this.rowPaddingBottom;
		}

		if (writer.context().page != ys[yi].page) {
			writer.context().page = ys[yi].page;

			//TODO: buggy, availableHeight should be updated on every pageChanged event
			// TableProcessor should be pageChanged listener, instead of processRow
			this.reservedAtBottom = 0;
		}

		// Draw horizontal lines before the vertical lines so they are not overridden
		if (willBreak && this.layout.hLineWhenBroken !== false) {
			this.drawHorizontalLine(rowIndex + 1, writer, y2);
		}
		if (rowBreakWithoutHeader && this.layout.hLineWhenBroken !== false) {
			this.drawHorizontalLine(rowIndex, writer, y1);
		}

		for (i = 0, l = xs.length; i < l; i++) {
			var leftCellBorder = false;
			var rightCellBorder = false;
			var colIndex = xs[i].index;

			// current cell
			if (colIndex < body[rowIndex].length) {
				var cell = body[rowIndex][colIndex];
				leftCellBorder = cell.border ? cell.border[0] : this.layout.defaultBorder;
				rightCellBorder = cell.border ? cell.border[2] : this.layout.defaultBorder;
			}

			// before cell
			if (colIndex > 0 && !leftCellBorder) {
				var cell = body[rowIndex][colIndex - 1];
				leftCellBorder = cell.border ? cell.border[2] : this.layout.defaultBorder;
			}

			// after cell
			if (colIndex + 1 < body[rowIndex].length && !rightCellBorder) {
				var cell = body[rowIndex][colIndex + 1];
				rightCellBorder = cell.border ? cell.border[0] : this.layout.defaultBorder;
			}

			if (leftCellBorder) {
				this.drawVerticalLine(xs[i].x, y1 - hzLineOffset, y2 + this.bottomLineWidth, xs[i].index, writer, rowIndex, xs[i - 1] ? xs[i - 1].index : null);
			}

			if (i < l - 1) {
				var fillColor = body[rowIndex][colIndex].fillColor;
				var fillOpacity = body[rowIndex][colIndex].fillOpacity;
				if (!fillColor) {
					fillColor = isFunction(this.layout.fillColor) ? this.layout.fillColor(rowIndex, this.tableNode, colIndex) : this.layout.fillColor;
				}
				if (!isNumber(fillOpacity)) {
					fillOpacity = isFunction(this.layout.fillOpacity) ? this.layout.fillOpacity(rowIndex, this.tableNode, colIndex) : this.layout.fillOpacity;
				}
				var overlayPattern = body[rowIndex][colIndex].overlayPattern;
				var overlayOpacity = body[rowIndex][colIndex].overlayOpacity;
				if (fillColor || overlayPattern) {
					var widthLeftBorder = leftCellBorder ? this.layout.vLineWidth(colIndex, this.tableNode) : 0;
					var widthRightBorder;
					if ((colIndex === 0 || colIndex + 1 == body[rowIndex].length) && !rightCellBorder) {
						widthRightBorder = this.layout.vLineWidth(colIndex + 1, this.tableNode);
					} else if (rightCellBorder) {
						widthRightBorder = this.layout.vLineWidth(colIndex + 1, this.tableNode) / 2;
					} else {
						widthRightBorder = 0;
					}

					var x1f = this.dontBreakRows ? xs[i].x + widthLeftBorder : xs[i].x + (widthLeftBorder / 2);
					var y1f = this.dontBreakRows ? y1 : y1 - (hzLineOffset / 2);
					var x2f = xs[i + 1].x + widthRightBorder;
					var y2f = this.dontBreakRows ? y2 + this.bottomLineWidth : y2 + (this.bottomLineWidth / 2);
					var bgWidth = x2f - x1f;
					var bgHeight = y2f - y1f;
					if (fillColor) {
						writer.addVector({
							type: 'rect',
							x: x1f,
							y: y1f,
							w: bgWidth,
							h: bgHeight,
							lineWidth: 0,
							color: fillColor,
							fillOpacity: fillOpacity,
							// mark if we are in an unbreakable block
							_isFillColorFromUnbreakable: !!writer.transactionLevel
						}, false, true, writer.context().backgroundLength[writer.context().page]);
					}

					if (overlayPattern) {
						writer.addVector({
							type: 'rect',
							x: x1f,
							y: y1f,
							w: bgWidth,
							h: bgHeight,
							lineWidth: 0,
							color: overlayPattern,
							fillOpacity: overlayOpacity
						}, false, true);
					}
				}
			}
		}
	}

	writer.context().page = endingPage;
	writer.context().y = endingY;

	var row = this.tableNode.table.body[rowIndex];
	for (i = 0, l = row.length; i < l; i++) {
		if (row[i].rowSpan) {
			this.rowSpanData[i].rowSpan = row[i].rowSpan;

			// fix colSpans
			if (row[i].colSpan && row[i].colSpan > 1) {
				for (var j = 1; j < row[i].rowSpan; j++) {
					this.tableNode.table.body[rowIndex + j][i]._colSpan = row[i].colSpan;
				}
			}
			// fix rowSpans
			if (row[i].rowSpan && row[i].rowSpan > 1) {
				for (var j = 1; j < row[i].rowSpan; j++) {
					this.tableNode.table.body[rowIndex + j][i]._rowSpanCurrentOffset = j;
				}
			}
		}

		if (this.rowSpanData[i].rowSpan > 0) {
			this.rowSpanData[i].rowSpan--;
		}
	}

	this.drawHorizontalLine(rowIndex + 1, writer);

	if (this.headerRows && rowIndex === this.headerRows - 1) {
		this.headerRepeatable = writer.currentBlockToRepeatable();
	}

	if (this.dontBreakRows) {
		writer.tracker.auto('pageChanged',
			function () {
				if (rowIndex > 0 && !self.headerRows && self.layout.hLineWhenBroken !== false) {
					// Draw the top border of the row after a page break
					self.drawHorizontalLine(rowIndex, writer);
				}
			},
			function () {
				writer.commitUnbreakableBlock();
			}
		);
	}

	if (this.headerRepeatable && (rowIndex === (this.rowsWithoutPageBreak - 1) || rowIndex === this.tableNode.table.body.length - 1)) {
		writer.commitUnbreakableBlock();
		writer.pushToRepeatables(this.headerRepeatable);
		this.cleanUpRepeatables = true;
		this.headerRepeatable = null;
	}

	function getLineXs() {
		var result = [];
		var cols = 0;

		for (var i = 0, l = self.tableNode.table.body[rowIndex].length; i < l; i++) {
			if (!cols) {
				result.push({ x: self.rowSpanData[i].left, index: i });

				var item = self.tableNode.table.body[rowIndex][i];
				cols = (item._colSpan || item.colSpan || 0);
			}
			if (cols > 0) {
				cols--;
			}
		}

		result.push({ x: self.rowSpanData[self.rowSpanData.length - 1].left, index: self.rowSpanData.length - 1 });

		return result;
	}
};

module.exports = TableProcessor;


/***/ }),

/***/ 84786:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isArray = (__webpack_require__(91867).isArray);
var isPattern = (__webpack_require__(91867).isPattern);
var getPattern = (__webpack_require__(91867).getPattern);

function groupDecorations(line) {
	var groups = [], currentGroup = null;
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		var decoration = inline.decoration;
		if (!decoration) {
			currentGroup = null;
			continue;
		}
		if (!isArray(decoration)) {
			decoration = [decoration];
		}
		var color = inline.decorationColor || inline.color || 'black';
		var style = inline.decorationStyle || 'solid';
		for (var ii = 0, ll = decoration.length; ii < ll; ii++) {
			var decorationItem = decoration[ii];
			if (!currentGroup || decorationItem !== currentGroup.decoration ||
				style !== currentGroup.decorationStyle || color !== currentGroup.decorationColor) {

				currentGroup = {
					line: line,
					decoration: decorationItem,
					decorationColor: color,
					decorationStyle: style,
					inlines: [inline]
				};
				groups.push(currentGroup);
			} else {
				currentGroup.inlines.push(inline);
			}
		}
	}

	return groups;
}

function drawDecoration(group, x, y, pdfKitDoc) {
	function maxInline() {
		var max = 0;
		for (var i = 0, l = group.inlines.length; i < l; i++) {
			var inline = group.inlines[i];
			max = inline.fontSize > max ? i : max;
		}
		return group.inlines[max];
	}
	function width() {
		var sum = 0;
		for (var i = 0, l = group.inlines.length; i < l; i++) {
			var justifyShift = (group.inlines[i].justifyShift || 0);
			sum += group.inlines[i].width + justifyShift;
		}
		return sum;
	}
	var firstInline = group.inlines[0],
		biggerInline = maxInline(),
		totalWidth = width(),
		lineAscent = group.line.getAscenderHeight(),
		ascent = biggerInline.font.ascender / 1000 * biggerInline.fontSize,
		height = biggerInline.height,
		descent = height - ascent;

	var lw = 0.5 + Math.floor(Math.max(biggerInline.fontSize - 8, 0) / 2) * 0.12;

	switch (group.decoration) {
		case 'underline':
			y += lineAscent + descent * 0.45;
			break;
		case 'overline':
			y += lineAscent - (ascent * 0.85);
			break;
		case 'lineThrough':
			y += lineAscent - (ascent * 0.25);
			break;
		default:
			throw 'Unknown decoration : ' + group.decoration;
	}
	pdfKitDoc.save();

	if (group.decorationStyle === 'double') {
		var gap = Math.max(0.5, lw * 2);
		pdfKitDoc.fillColor(group.decorationColor)
			.rect(x + firstInline.x, y - lw / 2, totalWidth, lw / 2).fill()
			.rect(x + firstInline.x, y + gap - lw / 2, totalWidth, lw / 2).fill();
	} else if (group.decorationStyle === 'dashed') {
		var nbDashes = Math.ceil(totalWidth / (3.96 + 2.84));
		var rdx = x + firstInline.x;
		pdfKitDoc.rect(rdx, y, totalWidth, lw).clip();
		pdfKitDoc.fillColor(group.decorationColor);
		for (var i = 0; i < nbDashes; i++) {
			pdfKitDoc.rect(rdx, y - lw / 2, 3.96, lw).fill();
			rdx += 3.96 + 2.84;
		}
	} else if (group.decorationStyle === 'dotted') {
		var nbDots = Math.ceil(totalWidth / (lw * 3));
		var rx = x + firstInline.x;
		pdfKitDoc.rect(rx, y, totalWidth, lw).clip();
		pdfKitDoc.fillColor(group.decorationColor);
		for (var ii = 0; ii < nbDots; ii++) {
			pdfKitDoc.rect(rx, y - lw / 2, lw, lw).fill();
			rx += (lw * 3);
		}
	} else if (group.decorationStyle === 'wavy') {
		var sh = 0.7, sv = 1;
		var nbWaves = Math.ceil(totalWidth / (sh * 2)) + 1;
		var rwx = x + firstInline.x - 1;
		pdfKitDoc.rect(x + firstInline.x, y - sv, totalWidth, y + sv).clip();
		pdfKitDoc.lineWidth(0.24);
		pdfKitDoc.moveTo(rwx, y);
		for (var iii = 0; iii < nbWaves; iii++) {
			pdfKitDoc.bezierCurveTo(rwx + sh, y - sv, rwx + sh * 2, y - sv, rwx + sh * 3, y)
				.bezierCurveTo(rwx + sh * 4, y + sv, rwx + sh * 5, y + sv, rwx + sh * 6, y);
			rwx += sh * 6;
		}
		pdfKitDoc.stroke(group.decorationColor);
	} else {
		pdfKitDoc.fillColor(group.decorationColor)
			.rect(x + firstInline.x, y - lw / 2, totalWidth, lw)
			.fill();
	}
	pdfKitDoc.restore();
}

function drawDecorations(line, x, y, pdfKitDoc) {
	var groups = groupDecorations(line);
	for (var i = 0, l = groups.length; i < l; i++) {
		drawDecoration(groups[i], x, y, pdfKitDoc);
	}
}

function drawBackground(line, x, y, patterns, pdfKitDoc) {
	var height = line.getHeight();
	for (var i = 0, l = line.inlines.length; i < l; i++) {
		var inline = line.inlines[i];
		if (!inline.background) {
			continue;
		}
		var color = inline.background;
		if (isPattern(inline.background)) {
			color = getPattern(inline.background, patterns);
		}
		var justifyShift = (inline.justifyShift || 0);
		pdfKitDoc.fillColor(color)
			.rect(x + inline.x - justifyShift, y, inline.width + justifyShift, height)
			.fill();
	}
}

module.exports = {
	drawBackground: drawBackground,
	drawDecorations: drawDecorations
};


/***/ }),

/***/ 11548:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var isString = (__webpack_require__(91867).isString);
var isNumber = (__webpack_require__(91867).isNumber);
var isObject = (__webpack_require__(91867).isObject);
var isArray = (__webpack_require__(91867).isArray);
var isUndefined = (__webpack_require__(91867).isUndefined);
var LineBreaker = __webpack_require__(5417);

var LEADING = /^(\s)+/g;
var TRAILING = /(\s)+$/g;

/**
 * Creates an instance of TextTools - text measurement utility
 *
 * @constructor
 * @param {FontProvider} fontProvider
 */
function TextTools(fontProvider) {
	this.fontProvider = fontProvider;
}

/**
 * Converts an array of strings (or inline-definition-objects) into a collection
 * of inlines and calculated minWidth/maxWidth.
 * and their min/max widths
 * @param  {Object} textArray - an array of inline-definition-objects (or strings)
 * @param  {Object} styleContextStack current style stack
 * @return {Object}                   collection of inlines, minWidth, maxWidth
 */
TextTools.prototype.buildInlines = function (textArray, styleContextStack) {
	var measured = measure(this.fontProvider, textArray, styleContextStack);

	var minWidth = 0,
		maxWidth = 0,
		currentLineWidth;

	measured.forEach(function (inline) {
		minWidth = Math.max(minWidth, inline.width - inline.leadingCut - inline.trailingCut);

		if (!currentLineWidth) {
			currentLineWidth = { width: 0, leadingCut: inline.leadingCut, trailingCut: 0 };
		}

		currentLineWidth.width += inline.width;
		currentLineWidth.trailingCut = inline.trailingCut;

		maxWidth = Math.max(maxWidth, getTrimmedWidth(currentLineWidth));

		if (inline.lineEnd) {
			currentLineWidth = null;
		}
	});

	if (getStyleProperty({}, styleContextStack, 'noWrap', false)) {
		minWidth = maxWidth;
	}

	return {
		items: measured,
		minWidth: minWidth,
		maxWidth: maxWidth
	};

	function getTrimmedWidth(item) {
		return Math.max(0, item.width - item.leadingCut - item.trailingCut);
	}
};

/**
 * Returns size of the specified string (without breaking it) using the current style
 * @param  {String} text              text to be measured
 * @param  {Object} styleContextStack current style stack
 * @return {Object}                   size of the specified string
 */
TextTools.prototype.sizeOfString = function (text, styleContextStack) {
	text = text ? text.toString().replace(/\t/g, '    ') : '';

	//TODO: refactor - extract from measure
	var fontName = getStyleProperty({}, styleContextStack, 'font', 'Roboto');
	var fontSize = getStyleProperty({}, styleContextStack, 'fontSize', 12);
	var fontFeatures = getStyleProperty({}, styleContextStack, 'fontFeatures', null);
	var bold = getStyleProperty({}, styleContextStack, 'bold', false);
	var italics = getStyleProperty({}, styleContextStack, 'italics', false);
	var lineHeight = getStyleProperty({}, styleContextStack, 'lineHeight', 1);
	var characterSpacing = getStyleProperty({}, styleContextStack, 'characterSpacing', 0);

	var font = this.fontProvider.provideFont(fontName, bold, italics);

	return {
		width: widthOfString(text, font, fontSize, characterSpacing, fontFeatures),
		height: font.lineHeight(fontSize) * lineHeight,
		fontSize: fontSize,
		lineHeight: lineHeight,
		ascender: font.ascender / 1000 * fontSize,
		descender: font.descender / 1000 * fontSize
	};
};

/**
 * Returns size of the specified rotated string (without breaking it) using the current style
 *
 * @param  {string} text text to be measured
 * @param  {number} angle
 * @param  {object} styleContextStack current style stack
 * @returns {object} size of the specified string
 */
TextTools.prototype.sizeOfRotatedText = function (text, angle, styleContextStack) {
	var angleRad = angle * Math.PI / -180;
	var size = this.sizeOfString(text, styleContextStack);
	return {
		width: Math.abs(size.height * Math.sin(angleRad)) + Math.abs(size.width * Math.cos(angleRad)),
		height: Math.abs(size.width * Math.sin(angleRad)) + Math.abs(size.height * Math.cos(angleRad))
	};
};

TextTools.prototype.widthOfString = function (text, font, fontSize, characterSpacing, fontFeatures) {
	return widthOfString(text, font, fontSize, characterSpacing, fontFeatures);
};

function splitWords(text, noWrap) {
	var results = [];
	text = text.replace(/\t/g, '    ');

	if (noWrap) {
		results.push({ text: text });
		return results;
	}

	var breaker = new LineBreaker(text);
	var last = 0;
	var bk;

	while (bk = breaker.nextBreak()) {
		var word = text.slice(last, bk.position);

		if (bk.required || word.match(/\r?\n$|\r$/)) { // new line
			word = word.replace(/\r?\n$|\r$/, '');
			results.push({ text: word, lineEnd: true });
		} else {
			results.push({ text: word });
		}

		last = bk.position;
	}

	return results;
}

function copyStyle(source, destination) {
	destination = destination || {};
	source = source || {}; //TODO: default style

	for (var key in source) {
		if (key != 'text' && source.hasOwnProperty(key)) {
			destination[key] = source[key];
		}
	}

	return destination;
}

function normalizeTextArray(array, styleContextStack) {
	function flatten(array) {
		return array.reduce(function (prev, cur) {
			var current = isArray(cur.text) ? flatten(cur.text) : cur;
			var more = [].concat(current).some(Array.isArray);
			return prev.concat(more ? flatten(current) : current);
		}, []);
	}

	function getOneWord(index, words, noWrap) {
		if (isUndefined(words[index])) {
			return null;
		}

		if (words[index].lineEnd) {
			return null;
		}

		var word = words[index].text;

		if (noWrap) {
			var tmpWords = splitWords(normalizeString(word), false);
			if (isUndefined(tmpWords[tmpWords.length - 1])) {
				return null;
			}
			word = tmpWords[tmpWords.length - 1].text;
		}

		return word;
	}

	var results = [];

	if (!isArray(array)) {
		array = [array];
	}

	array = flatten(array);

	var lastWord = null;
	for (var i = 0, l = array.length; i < l; i++) {
		var item = array[i];
		var style = null;
		var words;

		var noWrap = getStyleProperty(item || {}, styleContextStack, 'noWrap', false);
		if (isObject(item)) {
			if (item._textRef && item._textRef._textNodeRef.text) {
				item.text = item._textRef._textNodeRef.text;
			}
			words = splitWords(normalizeString(item.text), noWrap);
			style = copyStyle(item);
		} else {
			words = splitWords(normalizeString(item), noWrap);
		}

		if (lastWord && words.length) {
			var firstWord = getOneWord(0, words, noWrap);

			var wrapWords = splitWords(normalizeString(lastWord + firstWord), false);
			if (wrapWords.length === 1) {
				results[results.length - 1].noNewLine = true;
			}
		}

		for (var i2 = 0, l2 = words.length; i2 < l2; i2++) {
			var result = {
				text: words[i2].text
			};

			if (words[i2].lineEnd) {
				result.lineEnd = true;
			}

			copyStyle(style, result);

			results.push(result);
		}

		lastWord = null;
		if (i + 1 < l) {
			lastWord = getOneWord(words.length - 1, words, noWrap);
		}
	}

	return results;
}

function normalizeString(value) {
	if (value === undefined || value === null) {
		return '';
	} else if (isNumber(value)) {
		return value.toString();
	} else if (isString(value)) {
		return value;
	} else {
		return value.toString();
	}
}

function getStyleProperty(item, styleContextStack, property, defaultValue) {
	var value;

	if (item[property] !== undefined && item[property] !== null) {
		// item defines this property
		return item[property];
	}

	if (!styleContextStack) {
		return defaultValue;
	}

	styleContextStack.auto(item, function () {
		value = styleContextStack.getProperty(property);
	});

	if (value !== null && value !== undefined) {
		return value;
	} else {
		return defaultValue;
	}
}

function measure(fontProvider, textArray, styleContextStack) {
	var normalized = normalizeTextArray(textArray, styleContextStack);

	if (normalized.length) {
		var leadingIndent = getStyleProperty(normalized[0], styleContextStack, 'leadingIndent', 0);

		if (leadingIndent) {
			normalized[0].leadingCut = -leadingIndent;
			normalized[0].leadingIndent = leadingIndent;
		}
	}

	normalized.forEach(function (item) {
		var fontName = getStyleProperty(item, styleContextStack, 'font', 'Roboto');
		var fontSize = getStyleProperty(item, styleContextStack, 'fontSize', 12);
		var fontFeatures = getStyleProperty(item, styleContextStack, 'fontFeatures', null);
		var bold = getStyleProperty(item, styleContextStack, 'bold', false);
		var italics = getStyleProperty(item, styleContextStack, 'italics', false);
		var color = getStyleProperty(item, styleContextStack, 'color', 'black');
		var decoration = getStyleProperty(item, styleContextStack, 'decoration', null);
		var decorationColor = getStyleProperty(item, styleContextStack, 'decorationColor', null);
		var decorationStyle = getStyleProperty(item, styleContextStack, 'decorationStyle', null);
		var background = getStyleProperty(item, styleContextStack, 'background', null);
		var lineHeight = getStyleProperty(item, styleContextStack, 'lineHeight', 1);
		var characterSpacing = getStyleProperty(item, styleContextStack, 'characterSpacing', 0);
		var link = getStyleProperty(item, styleContextStack, 'link', null);
		var linkToPage = getStyleProperty(item, styleContextStack, 'linkToPage', null);
		var linkToDestination = getStyleProperty(item, styleContextStack, 'linkToDestination', null);
		var noWrap = getStyleProperty(item, styleContextStack, 'noWrap', null);
		var preserveLeadingSpaces = getStyleProperty(item, styleContextStack, 'preserveLeadingSpaces', false);
		var preserveTrailingSpaces = getStyleProperty(item, styleContextStack, 'preserveTrailingSpaces', false);
		var opacity = getStyleProperty(item, styleContextStack, 'opacity', 1);
		var sup = getStyleProperty(item, styleContextStack, 'sup', false);
		var sub = getStyleProperty(item, styleContextStack, 'sub', false);

		if ((sup || sub) && item.fontSize === undefined) {
			// font size reduction taken from here: https://en.wikipedia.org/wiki/Subscript_and_superscript#Desktop_publishing
			fontSize *= 0.58;
		}

		var font = fontProvider.provideFont(fontName, bold, italics);

		item.width = widthOfString(item.text, font, fontSize, characterSpacing, fontFeatures);
		item.height = font.lineHeight(fontSize) * lineHeight;

		if (!item.leadingCut) {
			item.leadingCut = 0;
		}

		var leadingSpaces;
		if (!preserveLeadingSpaces && (leadingSpaces = item.text.match(LEADING))) {
			item.leadingCut += widthOfString(leadingSpaces[0], font, fontSize, characterSpacing, fontFeatures);
		}

		var trailingSpaces;
		if (!preserveTrailingSpaces && (trailingSpaces = item.text.match(TRAILING))) {
			item.trailingCut = widthOfString(trailingSpaces[0], font, fontSize, characterSpacing, fontFeatures);
		} else {
			item.trailingCut = 0;
		}

		item.alignment = getStyleProperty(item, styleContextStack, 'alignment', 'left');
		item.font = font;
		item.fontSize = fontSize;
		item.fontFeatures = fontFeatures;
		item.characterSpacing = characterSpacing;
		item.color = color;
		item.decoration = decoration;
		item.decorationColor = decorationColor;
		item.decorationStyle = decorationStyle;
		item.background = background;
		item.link = link;
		item.linkToPage = linkToPage;
		item.linkToDestination = linkToDestination;
		item.noWrap = noWrap;
		item.opacity = opacity;
		item.sup = sup;
		item.sub = sub;
	});

	return normalized;
}

function widthOfString(text, font, fontSize, characterSpacing, fontFeatures) {
	return font.widthOfString(text, fontSize, fontFeatures) + ((characterSpacing || 0) * (text.length - 1));
}

module.exports = TextTools;


/***/ }),

/***/ 2318:
/***/ (function(module) {

"use strict";


function TraversalTracker() {
	this.events = {};
}

TraversalTracker.prototype.startTracking = function (event, callback) {
	var callbacks = this.events[event] || (this.events[event] = []);

	if (callbacks.indexOf(callback) < 0) {
		callbacks.push(callback);
	}
};

TraversalTracker.prototype.stopTracking = function (event, callback) {
	var callbacks = this.events[event];

	if (!callbacks) {
		return;
	}

	var index = callbacks.indexOf(callback);
	if (index >= 0) {
		callbacks.splice(index, 1);
	}
};

TraversalTracker.prototype.emit = function (event) {
	var args = Array.prototype.slice.call(arguments, 1);
	var callbacks = this.events[event];

	if (!callbacks) {
		return;
	}

	callbacks.forEach(function (callback) {
		callback.apply(this, args);
	});
};

TraversalTracker.prototype.auto = function (event, callback, innerFunction) {
	this.startTracking(event, callback);
	innerFunction();
	this.stopTracking(event, callback);
};

module.exports = TraversalTracker;


/***/ }),

/***/ 3502:
/***/ (function(module, exports, __webpack_require__) {

var __WEBPACK_AMD_DEFINE_FACTORY__, __WEBPACK_AMD_DEFINE_ARRAY__, __WEBPACK_AMD_DEFINE_RESULT__;(function(a,b){if(true)!(__WEBPACK_AMD_DEFINE_ARRAY__ = [], __WEBPACK_AMD_DEFINE_FACTORY__ = (b),
		__WEBPACK_AMD_DEFINE_RESULT__ = (typeof __WEBPACK_AMD_DEFINE_FACTORY__ === 'function' ?
		(__WEBPACK_AMD_DEFINE_FACTORY__.apply(exports, __WEBPACK_AMD_DEFINE_ARRAY__)) : __WEBPACK_AMD_DEFINE_FACTORY__),
		__WEBPACK_AMD_DEFINE_RESULT__ !== undefined && (module.exports = __WEBPACK_AMD_DEFINE_RESULT__));else {}})(this,function(){"use strict";function b(a,b){return"undefined"==typeof b?b={autoBom:!1}:"object"!=typeof b&&(console.warn("Deprecated: Expected third argument to be a object"),b={autoBom:!b}),b.autoBom&&/^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(a.type)?new Blob(["\uFEFF",a],{type:a.type}):a}function c(a,b,c){var d=new XMLHttpRequest;d.open("GET",a),d.responseType="blob",d.onload=function(){g(d.response,b,c)},d.onerror=function(){console.error("could not download file")},d.send()}function d(a){var b=new XMLHttpRequest;b.open("HEAD",a,!1);try{b.send()}catch(a){}return 200<=b.status&&299>=b.status}function e(a){try{a.dispatchEvent(new MouseEvent("click"))}catch(c){var b=document.createEvent("MouseEvents");b.initMouseEvent("click",!0,!0,window,0,0,0,80,20,!1,!1,!1,!1,0,null),a.dispatchEvent(b)}}var f="object"==typeof window&&window.window===window?window:"object"==typeof self&&self.self===self?self:"object"==typeof __webpack_require__.g&&__webpack_require__.g.global===__webpack_require__.g?__webpack_require__.g:void 0,a=f.navigator&&/Macintosh/.test(navigator.userAgent)&&/AppleWebKit/.test(navigator.userAgent)&&!/Safari/.test(navigator.userAgent),g=f.saveAs||("object"!=typeof window||window!==f?function(){}:(typeof HTMLAnchorElement !== "undefined" && "download" in HTMLAnchorElement.prototype)&&!a?function(b,g,h){var i=f.URL||f.webkitURL,j=document.createElement("a");g=g||b.name||"download",j.download=g,j.rel="noopener","string"==typeof b?(j.href=b,j.origin===location.origin?e(j):d(j.href)?c(b,g,h):e(j,j.target="_blank")):(j.href=i.createObjectURL(b),setTimeout(function(){i.revokeObjectURL(j.href)},4E4),setTimeout(function(){e(j)},0))}:"msSaveOrOpenBlob"in navigator?function(f,g,h){if(g=g||f.name||"download","string"!=typeof f)navigator.msSaveOrOpenBlob(b(f,h),g);else if(d(f))c(f,g,h);else{var i=document.createElement("a");i.href=f,i.target="_blank",setTimeout(function(){e(i)})}}:function(b,d,e,g){if(g=g||open("","_blank"),g&&(g.document.title=g.document.body.innerText="downloading..."),"string"==typeof b)return c(b,d,e);var h="application/octet-stream"===b.type,i=/constructor/i.test(f.HTMLElement)||f.safari,j=/CriOS\/[\d]+/.test(navigator.userAgent);if((j||h&&i||a)&&"undefined"!=typeof FileReader){var k=new FileReader;k.onloadend=function(){var a=k.result;a=j?a:a.replace(/^data:[^;]*;/,"data:attachment/file;"),g?g.location.href=a:location=a,g=null},k.readAsDataURL(b)}else{var l=f.URL||f.webkitURL,m=l.createObjectURL(b);g?g.location=m:location.href=m,g=null,setTimeout(function(){l.revokeObjectURL(m)},4E4)}});f.saveAs=g.saveAs=g, true&&(module.exports=g)});



/***/ }),

/***/ 10740:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";
/* provided dependency */ var Buffer = __webpack_require__(14598)["Buffer"];


__webpack_require__(65292);
__webpack_require__(73844);
__webpack_require__(69330);
__webpack_require__(5597);
__webpack_require__(7585);
__webpack_require__(39081);
__webpack_require__(41584);
__webpack_require__(11765);
__webpack_require__(81755);
__webpack_require__(94845);
__webpack_require__(80055);
__webpack_require__(20731);
__webpack_require__(45337);
__webpack_require__(23913);
__webpack_require__(8953);
__webpack_require__(24863);
__webpack_require__(18828);
__webpack_require__(7283);
__webpack_require__(58549);
__webpack_require__(18756);
__webpack_require__(75174);
__webpack_require__(57444);
__webpack_require__(37309);
__webpack_require__(14032);
__webpack_require__(68067);
__webpack_require__(61726);
__webpack_require__(74516);
__webpack_require__(57114);
__webpack_require__(76014);
__webpack_require__(65578);
__webpack_require__(58281);
__webpack_require__(6422);
__webpack_require__(28264);
__webpack_require__(72095);
__webpack_require__(75626);
__webpack_require__(47969);
__webpack_require__(73663);
__webpack_require__(29883);
__webpack_require__(35471);
__webpack_require__(21012);
__webpack_require__(88997);
__webpack_require__(97464);
__webpack_require__(2857);
__webpack_require__(94715);
__webpack_require__(13624);
__webpack_require__(91132);
__webpack_require__(62514);
__webpack_require__(24597);
__webpack_require__(88042);
__webpack_require__(4660);
__webpack_require__(92451);
__webpack_require__(44206);
__webpack_require__(66288);
__webpack_require__(13250);
__webpack_require__(3858);
__webpack_require__(84538);
__webpack_require__(64793);
__webpack_require__(74202);
__webpack_require__(52529);
__webpack_require__(42437);
__webpack_require__(94712);
var _;
function _defineProperties(e, r) { for (var t = 0; t < r.length; t++) { var o = r[t]; o.enumerable = o.enumerable || !1, o.configurable = !0, "value" in o && (o.writable = !0), Object.defineProperty(e, _toPropertyKey(o.key), o); } }
function _createClass(e, r, t) { return r && _defineProperties(e.prototype, r), t && _defineProperties(e, t), Object.defineProperty(e, "prototype", { writable: !1 }), e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
function _createForOfIteratorHelperLoose(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (t) return (t = t.call(r)).next.bind(t); if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var o = 0; return function () { return o >= r.length ? { done: !0 } : { done: !1, value: r[o++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _interopDefault(ex) {
  return ex && typeof ex === 'object' && 'default' in ex ? ex['default'] : ex;
}
var r = _interopDefault(__webpack_require__(62420));
var utils = __webpack_require__(18128);
var isEqual = _interopDefault(__webpack_require__(45728));
var unicode = _interopDefault(__webpack_require__(24766));
var UnicodeTrie = _interopDefault(__webpack_require__(59006));
var StateMachine = _interopDefault(__webpack_require__(18645));
var cloneDeep = _interopDefault(__webpack_require__(41613));
var inflate = _interopDefault(__webpack_require__(3483));
var brotli = _interopDefault(__webpack_require__(34460));
var fs = __webpack_require__(48181);
var fontkit = {};
fontkit.logErrors = false;
var formats = [];
fontkit.registerFormat = function (format) {
  formats.push(format);
};
fontkit.openSync = function (filename, postscriptName) {
  var buffer = fs.readFileSync(filename);
  return fontkit.create(buffer, postscriptName);
};
fontkit.open = function (filename, postscriptName, callback) {
  if (typeof postscriptName === 'function') {
    callback = postscriptName;
    postscriptName = null;
  }
  fs.readFile(filename, function (err, buffer) {
    if (err) {
      return callback(err);
    }
    try {
      var font = fontkit.create(buffer, postscriptName);
    } catch (e) {
      return callback(e);
    }
    return callback(null, font);
  });
  return;
};
fontkit.create = function (buffer, postscriptName) {
  for (var i = 0; i < formats.length; i++) {
    var format = formats[i];
    if (format.probe(buffer)) {
      var font = new format(new r.DecodeStream(buffer));
      if (postscriptName) {
        return font.getFont(postscriptName);
      }
      return font;
    }
  }
  throw new Error('Unknown font format');
};
fontkit.defaultLanguage = 'en';
fontkit.setDefaultLanguage = function (lang) {
  if (lang === void 0) {
    lang = 'en';
  }
  fontkit.defaultLanguage = lang;
};
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) {
  var desc = {};
  Object.keys(descriptor).forEach(function (key) {
    desc[key] = descriptor[key];
  });
  desc.enumerable = !!desc.enumerable;
  desc.configurable = !!desc.configurable;
  if ('value' in desc || desc.initializer) {
    desc.writable = true;
  }
  desc = decorators.slice().reverse().reduce(function (desc, decorator) {
    return decorator(target, property, desc) || desc;
  }, desc);
  if (context && desc.initializer !== void 0) {
    desc.value = desc.initializer ? desc.initializer.call(context) : void 0;
    desc.initializer = undefined;
  }
  if (desc.initializer === void 0) {
    Object.defineProperty(target, property, desc);
    desc = null;
  }
  return desc;
}

/**
 * This decorator caches the results of a getter or method such that
 * the results are lazily computed once, and then cached.
 * @private
 */
function cache(target, key, descriptor) {
  if (descriptor.get) {
    var get = descriptor.get;
    descriptor.get = function () {
      var value = get.call(this);
      Object.defineProperty(this, key, {
        value: value
      });
      return value;
    };
  } else if (typeof descriptor.value === 'function') {
    var fn = descriptor.value;
    return {
      get: function get() {
        var cache = new Map();
        function memoized() {
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }
          var key = args.length > 0 ? args[0] : 'value';
          if (cache.has(key)) {
            return cache.get(key);
          }
          var result = fn.apply(this, args);
          cache.set(key, result);
          return result;
        }
        Object.defineProperty(this, key, {
          value: memoized
        });
        return memoized;
      }
    };
  }
}
var SubHeader = new r.Struct({
  firstCode: r.uint16,
  entryCount: r.uint16,
  idDelta: r.int16,
  idRangeOffset: r.uint16
});
var CmapGroup = new r.Struct({
  startCharCode: r.uint32,
  endCharCode: r.uint32,
  glyphID: r.uint32
});
var UnicodeValueRange = new r.Struct({
  startUnicodeValue: r.uint24,
  additionalCount: r.uint8
});
var UVSMapping = new r.Struct({
  unicodeValue: r.uint24,
  glyphID: r.uint16
});
var DefaultUVS = new r.Array(UnicodeValueRange, r.uint32);
var NonDefaultUVS = new r.Array(UVSMapping, r.uint32);
var VarSelectorRecord = new r.Struct({
  varSelector: r.uint24,
  defaultUVS: new r.Pointer(r.uint32, DefaultUVS, {
    type: 'parent'
  }),
  nonDefaultUVS: new r.Pointer(r.uint32, NonDefaultUVS, {
    type: 'parent'
  })
});
var CmapSubtable = new r.VersionedStruct(r.uint16, {
  0: {
    // Byte encoding
    length: r.uint16,
    // Total table length in bytes (set to 262 for format 0)
    language: r.uint16,
    // Language code for this encoding subtable, or zero if language-independent
    codeMap: new r.LazyArray(r.uint8, 256)
  },
  2: {
    // High-byte mapping (CJK)
    length: r.uint16,
    language: r.uint16,
    subHeaderKeys: new r.Array(r.uint16, 256),
    subHeaderCount: function subHeaderCount(t) {
      return Math.max.apply(Math, t.subHeaderKeys);
    },
    subHeaders: new r.LazyArray(SubHeader, 'subHeaderCount'),
    glyphIndexArray: new r.LazyArray(r.uint16, 'subHeaderCount')
  },
  4: {
    // Segment mapping to delta values
    length: r.uint16,
    // Total table length in bytes
    language: r.uint16,
    // Language code
    segCountX2: r.uint16,
    segCount: function segCount(t) {
      return t.segCountX2 >> 1;
    },
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16,
    endCode: new r.LazyArray(r.uint16, 'segCount'),
    reservedPad: new r.Reserved(r.uint16),
    // This value should be zero
    startCode: new r.LazyArray(r.uint16, 'segCount'),
    idDelta: new r.LazyArray(r.int16, 'segCount'),
    idRangeOffset: new r.LazyArray(r.uint16, 'segCount'),
    glyphIndexArray: new r.LazyArray(r.uint16, function (t) {
      return (t.length - t._currentOffset) / 2;
    })
  },
  6: {
    // Trimmed table
    length: r.uint16,
    language: r.uint16,
    firstCode: r.uint16,
    entryCount: r.uint16,
    glyphIndices: new r.LazyArray(r.uint16, 'entryCount')
  },
  8: {
    // mixed 16-bit and 32-bit coverage
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint16,
    is32: new r.LazyArray(r.uint8, 8192),
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },
  10: {
    // Trimmed Array
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    firstCode: r.uint32,
    entryCount: r.uint32,
    glyphIndices: new r.LazyArray(r.uint16, 'numChars')
  },
  12: {
    // Segmented coverage
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },
  13: {
    // Many-to-one range mappings (same as 12 except for group.startGlyphID)
    reserved: new r.Reserved(r.uint16),
    length: r.uint32,
    language: r.uint32,
    nGroups: r.uint32,
    groups: new r.LazyArray(CmapGroup, 'nGroups')
  },
  14: {
    // Unicode Variation Sequences
    length: r.uint32,
    numRecords: r.uint32,
    varSelectors: new r.LazyArray(VarSelectorRecord, 'numRecords')
  }
});
var CmapEntry = new r.Struct({
  platformID: r.uint16,
  // Platform identifier
  encodingID: r.uint16,
  // Platform-specific encoding identifier
  table: new r.Pointer(r.uint32, CmapSubtable, {
    type: 'parent',
    lazy: true
  })
}); // character to glyph mapping

var cmap = new r.Struct({
  version: r.uint16,
  numSubtables: r.uint16,
  tables: new r.Array(CmapEntry, 'numSubtables')
});
var head = new r.Struct({
  version: r.int32,
  // 0x00010000 (version 1.0)
  revision: r.int32,
  // set by font manufacturer
  checkSumAdjustment: r.uint32,
  magicNumber: r.uint32,
  // set to 0x5F0F3CF5
  flags: r.uint16,
  unitsPerEm: r.uint16,
  // range from 64 to 16384
  created: new r.Array(r.int32, 2),
  modified: new r.Array(r.int32, 2),
  xMin: r.int16,
  // for all glyph bounding boxes
  yMin: r.int16,
  // for all glyph bounding boxes
  xMax: r.int16,
  // for all glyph bounding boxes
  yMax: r.int16,
  // for all glyph bounding boxes
  macStyle: new r.Bitfield(r.uint16, ['bold', 'italic', 'underline', 'outline', 'shadow', 'condensed', 'extended']),
  lowestRecPPEM: r.uint16,
  // smallest readable size in pixels
  fontDirectionHint: r.int16,
  indexToLocFormat: r.int16,
  // 0 for short offsets, 1 for long
  glyphDataFormat: r.int16 // 0 for current format
});
var hhea = new r.Struct({
  version: r.int32,
  ascent: r.int16,
  // Distance from baseline of highest ascender
  descent: r.int16,
  // Distance from baseline of lowest descender
  lineGap: r.int16,
  // Typographic line gap
  advanceWidthMax: r.uint16,
  // Maximum advance width value in 'hmtx' table
  minLeftSideBearing: r.int16,
  // Maximum advance width value in 'hmtx' table
  minRightSideBearing: r.int16,
  // Minimum right sidebearing value
  xMaxExtent: r.int16,
  caretSlopeRise: r.int16,
  // Used to calculate the slope of the cursor (rise/run); 1 for vertical
  caretSlopeRun: r.int16,
  // 0 for vertical
  caretOffset: r.int16,
  // Set to 0 for non-slanted fonts
  reserved: new r.Reserved(r.int16, 4),
  metricDataFormat: r.int16,
  // 0 for current format
  numberOfMetrics: r.uint16 // Number of advance widths in 'hmtx' table
});
var HmtxEntry = new r.Struct({
  advance: r.uint16,
  bearing: r.int16
});
var hmtx = new r.Struct({
  metrics: new r.LazyArray(HmtxEntry, function (t) {
    return t.parent.hhea.numberOfMetrics;
  }),
  bearings: new r.LazyArray(r.int16, function (t) {
    return t.parent.maxp.numGlyphs - t.parent.hhea.numberOfMetrics;
  })
});
var maxp = new r.Struct({
  version: r.int32,
  numGlyphs: r.uint16,
  // The number of glyphs in the font
  maxPoints: r.uint16,
  // Maximum points in a non-composite glyph
  maxContours: r.uint16,
  // Maximum contours in a non-composite glyph
  maxComponentPoints: r.uint16,
  // Maximum points in a composite glyph
  maxComponentContours: r.uint16,
  // Maximum contours in a composite glyph
  maxZones: r.uint16,
  // 1 if instructions do not use the twilight zone, 2 otherwise
  maxTwilightPoints: r.uint16,
  // Maximum points used in Z0
  maxStorage: r.uint16,
  // Number of Storage Area locations
  maxFunctionDefs: r.uint16,
  // Number of FDEFs
  maxInstructionDefs: r.uint16,
  // Number of IDEFs
  maxStackElements: r.uint16,
  // Maximum stack depth
  maxSizeOfInstructions: r.uint16,
  // Maximum byte count for glyph instructions
  maxComponentElements: r.uint16,
  // Maximum number of components referenced at “top level” for any composite glyph
  maxComponentDepth: r.uint16 // Maximum levels of recursion; 1 for simple components
});

/**
 * Gets an encoding name from platform, encoding, and language ids.
 * Returned encoding names can be used in iconv-lite to decode text.
 */
function getEncoding(platformID, encodingID, languageID) {
  if (languageID === void 0) {
    languageID = 0;
  }
  if (platformID === 1 && MAC_LANGUAGE_ENCODINGS[languageID]) {
    return MAC_LANGUAGE_ENCODINGS[languageID];
  }
  return ENCODINGS[platformID][encodingID];
} // Map of platform ids to encoding ids.

var ENCODINGS = [
// unicode
['utf16be', 'utf16be', 'utf16be', 'utf16be', 'utf16be', 'utf16be'],
// macintosh
// Mappings available at http://unicode.org/Public/MAPPINGS/VENDORS/APPLE/
// 0	Roman                 17	Malayalam
// 1	Japanese	            18	Sinhalese
// 2	Traditional Chinese	  19	Burmese
// 3	Korean	              20	Khmer
// 4	Arabic	              21	Thai
// 5	Hebrew	              22	Laotian
// 6	Greek	                23	Georgian
// 7	Russian	              24	Armenian
// 8	RSymbol	              25	Simplified Chinese
// 9	Devanagari	          26	Tibetan
// 10	Gurmukhi	            27	Mongolian
// 11	Gujarati	            28	Geez
// 12	Oriya	                29	Slavic
// 13	Bengali	              30	Vietnamese
// 14	Tamil	                31	Sindhi
// 15	Telugu	              32	(Uninterpreted)
// 16	Kannada
['macroman', 'shift-jis', 'big5', 'euc-kr', 'iso-8859-6', 'iso-8859-8', 'macgreek', 'maccyrillic', 'symbol', 'Devanagari', 'Gurmukhi', 'Gujarati', 'Oriya', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Sinhalese', 'Burmese', 'Khmer', 'macthai', 'Laotian', 'Georgian', 'Armenian', 'gb-2312-80', 'Tibetan', 'Mongolian', 'Geez', 'maccenteuro', 'Vietnamese', 'Sindhi'],
// ISO (deprecated)
['ascii'],
// windows
// Docs here: http://msdn.microsoft.com/en-us/library/system.text.encoding(v=vs.110).aspx
['symbol', 'utf16be', 'shift-jis', 'gb18030', 'big5', 'wansung', 'johab', null, null, null, 'utf16be']]; // Overrides for Mac scripts by language id.
// See http://unicode.org/Public/MAPPINGS/VENDORS/APPLE/Readme.txt

var MAC_LANGUAGE_ENCODINGS = {
  15: 'maciceland',
  17: 'macturkish',
  18: 'maccroatian',
  24: 'maccenteuro',
  25: 'maccenteuro',
  26: 'maccenteuro',
  27: 'maccenteuro',
  28: 'maccenteuro',
  30: 'maciceland',
  37: 'macromania',
  38: 'maccenteuro',
  39: 'maccenteuro',
  40: 'maccenteuro',
  143: 'macinuit',
  // Unsupported by iconv-lite
  146: 'macgaelic' // Unsupported by iconv-lite
}; // Map of platform ids to BCP-47 language codes.

var LANGUAGES = [
// unicode
[], {
  // macintosh
  0: 'en',
  30: 'fo',
  60: 'ks',
  90: 'rw',
  1: 'fr',
  31: 'fa',
  61: 'ku',
  91: 'rn',
  2: 'de',
  32: 'ru',
  62: 'sd',
  92: 'ny',
  3: 'it',
  33: 'zh',
  63: 'bo',
  93: 'mg',
  4: 'nl',
  34: 'nl-BE',
  64: 'ne',
  94: 'eo',
  5: 'sv',
  35: 'ga',
  65: 'sa',
  128: 'cy',
  6: 'es',
  36: 'sq',
  66: 'mr',
  129: 'eu',
  7: 'da',
  37: 'ro',
  67: 'bn',
  130: 'ca',
  8: 'pt',
  38: 'cz',
  68: 'as',
  131: 'la',
  9: 'no',
  39: 'sk',
  69: 'gu',
  132: 'qu',
  10: 'he',
  40: 'si',
  70: 'pa',
  133: 'gn',
  11: 'ja',
  41: 'yi',
  71: 'or',
  134: 'ay',
  12: 'ar',
  42: 'sr',
  72: 'ml',
  135: 'tt',
  13: 'fi',
  43: 'mk',
  73: 'kn',
  136: 'ug',
  14: 'el',
  44: 'bg',
  74: 'ta',
  137: 'dz',
  15: 'is',
  45: 'uk',
  75: 'te',
  138: 'jv',
  16: 'mt',
  46: 'be',
  76: 'si',
  139: 'su',
  17: 'tr',
  47: 'uz',
  77: 'my',
  140: 'gl',
  18: 'hr',
  48: 'kk',
  78: 'km',
  141: 'af',
  19: 'zh-Hant',
  49: 'az-Cyrl',
  79: 'lo',
  142: 'br',
  20: 'ur',
  50: 'az-Arab',
  80: 'vi',
  143: 'iu',
  21: 'hi',
  51: 'hy',
  81: 'id',
  144: 'gd',
  22: 'th',
  52: 'ka',
  82: 'tl',
  145: 'gv',
  23: 'ko',
  53: 'mo',
  83: 'ms',
  146: 'ga',
  24: 'lt',
  54: 'ky',
  84: 'ms-Arab',
  147: 'to',
  25: 'pl',
  55: 'tg',
  85: 'am',
  148: 'el-polyton',
  26: 'hu',
  56: 'tk',
  86: 'ti',
  149: 'kl',
  27: 'es',
  57: 'mn-CN',
  87: 'om',
  150: 'az',
  28: 'lv',
  58: 'mn',
  88: 'so',
  151: 'nn',
  29: 'se',
  59: 'ps',
  89: 'sw'
},
// ISO (deprecated)
[], {
  // windows                                        
  0x0436: 'af',
  0x4009: 'en-IN',
  0x0487: 'rw',
  0x0432: 'tn',
  0x041C: 'sq',
  0x1809: 'en-IE',
  0x0441: 'sw',
  0x045B: 'si',
  0x0484: 'gsw',
  0x2009: 'en-JM',
  0x0457: 'kok',
  0x041B: 'sk',
  0x045E: 'am',
  0x4409: 'en-MY',
  0x0412: 'ko',
  0x0424: 'sl',
  0x1401: 'ar-DZ',
  0x1409: 'en-NZ',
  0x0440: 'ky',
  0x2C0A: 'es-AR',
  0x3C01: 'ar-BH',
  0x3409: 'en-PH',
  0x0454: 'lo',
  0x400A: 'es-BO',
  0x0C01: 'ar',
  0x4809: 'en-SG',
  0x0426: 'lv',
  0x340A: 'es-CL',
  0x0801: 'ar-IQ',
  0x1C09: 'en-ZA',
  0x0427: 'lt',
  0x240A: 'es-CO',
  0x2C01: 'ar-JO',
  0x2C09: 'en-TT',
  0x082E: 'dsb',
  0x140A: 'es-CR',
  0x3401: 'ar-KW',
  0x0809: 'en-GB',
  0x046E: 'lb',
  0x1C0A: 'es-DO',
  0x3001: 'ar-LB',
  0x0409: 'en',
  0x042F: 'mk',
  0x300A: 'es-EC',
  0x1001: 'ar-LY',
  0x3009: 'en-ZW',
  0x083E: 'ms-BN',
  0x440A: 'es-SV',
  0x1801: 'ary',
  0x0425: 'et',
  0x043E: 'ms',
  0x100A: 'es-GT',
  0x2001: 'ar-OM',
  0x0438: 'fo',
  0x044C: 'ml',
  0x480A: 'es-HN',
  0x4001: 'ar-QA',
  0x0464: 'fil',
  0x043A: 'mt',
  0x080A: 'es-MX',
  0x0401: 'ar-SA',
  0x040B: 'fi',
  0x0481: 'mi',
  0x4C0A: 'es-NI',
  0x2801: 'ar-SY',
  0x080C: 'fr-BE',
  0x047A: 'arn',
  0x180A: 'es-PA',
  0x1C01: 'aeb',
  0x0C0C: 'fr-CA',
  0x044E: 'mr',
  0x3C0A: 'es-PY',
  0x3801: 'ar-AE',
  0x040C: 'fr',
  0x047C: 'moh',
  0x280A: 'es-PE',
  0x2401: 'ar-YE',
  0x140C: 'fr-LU',
  0x0450: 'mn',
  0x500A: 'es-PR',
  0x042B: 'hy',
  0x180C: 'fr-MC',
  0x0850: 'mn-CN',
  0x0C0A: 'es',
  0x044D: 'as',
  0x100C: 'fr-CH',
  0x0461: 'ne',
  0x040A: 'es',
  0x082C: 'az-Cyrl',
  0x0462: 'fy',
  0x0414: 'nb',
  0x540A: 'es-US',
  0x042C: 'az',
  0x0456: 'gl',
  0x0814: 'nn',
  0x380A: 'es-UY',
  0x046D: 'ba',
  0x0437: 'ka',
  0x0482: 'oc',
  0x200A: 'es-VE',
  0x042D: 'eu',
  0x0C07: 'de-AT',
  0x0448: 'or',
  0x081D: 'sv-FI',
  0x0423: 'be',
  0x0407: 'de',
  0x0463: 'ps',
  0x041D: 'sv',
  0x0845: 'bn',
  0x1407: 'de-LI',
  0x0415: 'pl',
  0x045A: 'syr',
  0x0445: 'bn-IN',
  0x1007: 'de-LU',
  0x0416: 'pt',
  0x0428: 'tg',
  0x201A: 'bs-Cyrl',
  0x0807: 'de-CH',
  0x0816: 'pt-PT',
  0x085F: 'tzm',
  0x141A: 'bs',
  0x0408: 'el',
  0x0446: 'pa',
  0x0449: 'ta',
  0x047E: 'br',
  0x046F: 'kl',
  0x046B: 'qu-BO',
  0x0444: 'tt',
  0x0402: 'bg',
  0x0447: 'gu',
  0x086B: 'qu-EC',
  0x044A: 'te',
  0x0403: 'ca',
  0x0468: 'ha',
  0x0C6B: 'qu',
  0x041E: 'th',
  0x0C04: 'zh-HK',
  0x040D: 'he',
  0x0418: 'ro',
  0x0451: 'bo',
  0x1404: 'zh-MO',
  0x0439: 'hi',
  0x0417: 'rm',
  0x041F: 'tr',
  0x0804: 'zh',
  0x040E: 'hu',
  0x0419: 'ru',
  0x0442: 'tk',
  0x1004: 'zh-SG',
  0x040F: 'is',
  0x243B: 'smn',
  0x0480: 'ug',
  0x0404: 'zh-TW',
  0x0470: 'ig',
  0x103B: 'smj-NO',
  0x0422: 'uk',
  0x0483: 'co',
  0x0421: 'id',
  0x143B: 'smj',
  0x042E: 'hsb',
  0x041A: 'hr',
  0x045D: 'iu',
  0x0C3B: 'se-FI',
  0x0420: 'ur',
  0x101A: 'hr-BA',
  0x085D: 'iu-Latn',
  0x043B: 'se',
  0x0843: 'uz-Cyrl',
  0x0405: 'cs',
  0x083C: 'ga',
  0x083B: 'se-SE',
  0x0443: 'uz',
  0x0406: 'da',
  0x0434: 'xh',
  0x203B: 'sms',
  0x042A: 'vi',
  0x048C: 'prs',
  0x0435: 'zu',
  0x183B: 'sma-NO',
  0x0452: 'cy',
  0x0465: 'dv',
  0x0410: 'it',
  0x1C3B: 'sms',
  0x0488: 'wo',
  0x0813: 'nl-BE',
  0x0810: 'it-CH',
  0x044F: 'sa',
  0x0485: 'sah',
  0x0413: 'nl',
  0x0411: 'ja',
  0x1C1A: 'sr-Cyrl-BA',
  0x0478: 'ii',
  0x0C09: 'en-AU',
  0x044B: 'kn',
  0x0C1A: 'sr',
  0x046A: 'yo',
  0x2809: 'en-BZ',
  0x043F: 'kk',
  0x181A: 'sr-Latn-BA',
  0x1009: 'en-CA',
  0x0453: 'km',
  0x081A: 'sr-Latn',
  0x2409: 'en-029',
  0x0486: 'quc',
  0x046C: 'nso'
}];
var NameRecord = new r.Struct({
  platformID: r.uint16,
  encodingID: r.uint16,
  languageID: r.uint16,
  nameID: r.uint16,
  length: r.uint16,
  string: new r.Pointer(r.uint16, new r.String('length', function (t) {
    return getEncoding(t.platformID, t.encodingID, t.languageID);
  }), {
    type: 'parent',
    relativeTo: function relativeTo(ctx) {
      return ctx.parent.stringOffset;
    },
    allowNull: false
  })
});
var LangTagRecord = new r.Struct({
  length: r.uint16,
  tag: new r.Pointer(r.uint16, new r.String('length', 'utf16be'), {
    type: 'parent',
    relativeTo: function relativeTo(ctx) {
      return ctx.stringOffset;
    }
  })
});
var NameTable = new r.VersionedStruct(r.uint16, {
  0: {
    count: r.uint16,
    stringOffset: r.uint16,
    records: new r.Array(NameRecord, 'count')
  },
  1: {
    count: r.uint16,
    stringOffset: r.uint16,
    records: new r.Array(NameRecord, 'count'),
    langTagCount: r.uint16,
    langTags: new r.Array(LangTagRecord, 'langTagCount')
  }
});
var NAMES = ['copyright', 'fontFamily', 'fontSubfamily', 'uniqueSubfamily', 'fullName', 'version', 'postscriptName',
// Note: A font may have only one PostScript name and that name must be ASCII.
'trademark', 'manufacturer', 'designer', 'description', 'vendorURL', 'designerURL', 'license', 'licenseURL', null,
// reserved
'preferredFamily', 'preferredSubfamily', 'compatibleFull', 'sampleText', 'postscriptCIDFontName', 'wwsFamilyName', 'wwsSubfamilyName'];
NameTable.process = function (stream) {
  var records = {};
  for (var _iterator = _createForOfIteratorHelperLoose(this.records), _step; !(_step = _iterator()).done;) {
    var record = _step.value;
    // find out what language this is for
    var language = LANGUAGES[record.platformID][record.languageID];
    if (language == null && this.langTags != null && record.languageID >= 0x8000) {
      language = this.langTags[record.languageID - 0x8000].tag;
    }
    if (language == null) {
      language = record.platformID + '-' + record.languageID;
    } // if the nameID is >= 256, it is a font feature record (AAT)

    var key = record.nameID >= 256 ? 'fontFeatures' : NAMES[record.nameID] || record.nameID;
    if (records[key] == null) {
      records[key] = {};
    }
    var obj = records[key];
    if (record.nameID >= 256) {
      obj = obj[record.nameID] || (obj[record.nameID] = {});
    }
    if (typeof record.string === 'string' || typeof obj[language] !== 'string') {
      obj[language] = record.string;
    }
  }
  this.records = records;
};
NameTable.preEncode = function () {
  if (Array.isArray(this.records)) return;
  this.version = 0;
  var records = [];
  for (var key in this.records) {
    var val = this.records[key];
    if (key === 'fontFeatures') continue;
    records.push({
      platformID: 3,
      encodingID: 1,
      languageID: 0x409,
      nameID: NAMES.indexOf(key),
      length: Buffer.byteLength(val.en, 'utf16le'),
      string: val.en
    });
    if (key === 'postscriptName') {
      records.push({
        platformID: 1,
        encodingID: 0,
        languageID: 0,
        nameID: NAMES.indexOf(key),
        length: val.en.length,
        string: val.en
      });
    }
  }
  this.records = records;
  this.count = records.length;
  this.stringOffset = NameTable.size(this, null, false);
};
var OS2 = new r.VersionedStruct(r.uint16, {
  header: {
    xAvgCharWidth: r.int16,
    // average weighted advance width of lower case letters and space
    usWeightClass: r.uint16,
    // visual weight of stroke in glyphs
    usWidthClass: r.uint16,
    // relative change from the normal aspect ratio (width to height ratio)
    fsType: new r.Bitfield(r.uint16, [
    // Indicates font embedding licensing rights
    null, 'noEmbedding', 'viewOnly', 'editable', null, null, null, null, 'noSubsetting', 'bitmapOnly']),
    ySubscriptXSize: r.int16,
    // recommended horizontal size in pixels for subscripts
    ySubscriptYSize: r.int16,
    // recommended vertical size in pixels for subscripts
    ySubscriptXOffset: r.int16,
    // recommended horizontal offset for subscripts
    ySubscriptYOffset: r.int16,
    // recommended vertical offset form the baseline for subscripts
    ySuperscriptXSize: r.int16,
    // recommended horizontal size in pixels for superscripts
    ySuperscriptYSize: r.int16,
    // recommended vertical size in pixels for superscripts
    ySuperscriptXOffset: r.int16,
    // recommended horizontal offset for superscripts
    ySuperscriptYOffset: r.int16,
    // recommended vertical offset from the baseline for superscripts
    yStrikeoutSize: r.int16,
    // width of the strikeout stroke
    yStrikeoutPosition: r.int16,
    // position of the strikeout stroke relative to the baseline
    sFamilyClass: r.int16,
    // classification of font-family design
    panose: new r.Array(r.uint8, 10),
    // describe the visual characteristics of a given typeface
    ulCharRange: new r.Array(r.uint32, 4),
    vendorID: new r.String(4),
    // four character identifier for the font vendor
    fsSelection: new r.Bitfield(r.uint16, [
    // bit field containing information about the font
    'italic', 'underscore', 'negative', 'outlined', 'strikeout', 'bold', 'regular', 'useTypoMetrics', 'wws', 'oblique']),
    usFirstCharIndex: r.uint16,
    // The minimum Unicode index in this font
    usLastCharIndex: r.uint16 // The maximum Unicode index in this font
  },
  // The Apple version of this table ends here, but the Microsoft one continues on...
  0: {},
  1: {
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2)
  },
  2: {
    // these should be common with version 1 somehow
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2),
    xHeight: r.int16,
    capHeight: r.int16,
    defaultChar: r.uint16,
    breakChar: r.uint16,
    maxContent: r.uint16
  },
  5: {
    typoAscender: r.int16,
    typoDescender: r.int16,
    typoLineGap: r.int16,
    winAscent: r.uint16,
    winDescent: r.uint16,
    codePageRange: new r.Array(r.uint32, 2),
    xHeight: r.int16,
    capHeight: r.int16,
    defaultChar: r.uint16,
    breakChar: r.uint16,
    maxContent: r.uint16,
    usLowerOpticalPointSize: r.uint16,
    usUpperOpticalPointSize: r.uint16
  }
});
var versions = OS2.versions;
versions[3] = versions[4] = versions[2];
var post = new r.VersionedStruct(r.fixed32, {
  header: {
    // these fields exist at the top of all versions
    italicAngle: r.fixed32,
    // Italic angle in counter-clockwise degrees from the vertical.
    underlinePosition: r.int16,
    // Suggested distance of the top of the underline from the baseline
    underlineThickness: r.int16,
    // Suggested values for the underline thickness
    isFixedPitch: r.uint32,
    // Whether the font is monospaced
    minMemType42: r.uint32,
    // Minimum memory usage when a TrueType font is downloaded as a Type 42 font
    maxMemType42: r.uint32,
    // Maximum memory usage when a TrueType font is downloaded as a Type 42 font
    minMemType1: r.uint32,
    // Minimum memory usage when a TrueType font is downloaded as a Type 1 font
    maxMemType1: r.uint32 // Maximum memory usage when a TrueType font is downloaded as a Type 1 font
  },
  1: {},
  // version 1 has no additional fields
  2: {
    numberOfGlyphs: r.uint16,
    glyphNameIndex: new r.Array(r.uint16, 'numberOfGlyphs'),
    names: new r.Array(new r.String(r.uint8))
  },
  2.5: {
    numberOfGlyphs: r.uint16,
    offsets: new r.Array(r.uint8, 'numberOfGlyphs')
  },
  3: {},
  // version 3 has no additional fields
  4: {
    map: new r.Array(r.uint32, function (t) {
      return t.parent.maxp.numGlyphs;
    })
  }
});
var cvt = new r.Struct({
  controlValues: new r.Array(r.int16)
});

// These instructions are known as the font program. The main use of this table
// is for the definition of functions that are used in many different glyph programs.

var fpgm = new r.Struct({
  instructions: new r.Array(r.uint8)
});
var loca = new r.VersionedStruct('head.indexToLocFormat', {
  0: {
    offsets: new r.Array(r.uint16)
  },
  1: {
    offsets: new r.Array(r.uint32)
  }
});
loca.process = function () {
  if (this.version === 0) {
    for (var i = 0; i < this.offsets.length; i++) {
      this.offsets[i] <<= 1;
    }
  }
};
loca.preEncode = function () {
  if (this.version === 0) {
    for (var i = 0; i < this.offsets.length; i++) {
      this.offsets[i] >>>= 1;
    }
  }
};
var prep = new r.Struct({
  controlValueProgram: new r.Array(r.uint8)
});
var glyf = new r.Array(new r.Buffer());
var CFFIndex = /*#__PURE__*/function () {
  function CFFIndex(type) {
    this.type = type;
  }
  var _proto = CFFIndex.prototype;
  _proto.getCFFVersion = function getCFFVersion(ctx) {
    while (ctx && !ctx.hdrSize) {
      ctx = ctx.parent;
    }
    return ctx ? ctx.version : -1;
  };
  _proto.decode = function decode(stream, parent) {
    var version = this.getCFFVersion(parent);
    var count = version >= 2 ? stream.readUInt32BE() : stream.readUInt16BE();
    if (count === 0) {
      return [];
    }
    var offSize = stream.readUInt8();
    var offsetType;
    if (offSize === 1) {
      offsetType = r.uint8;
    } else if (offSize === 2) {
      offsetType = r.uint16;
    } else if (offSize === 3) {
      offsetType = r.uint24;
    } else if (offSize === 4) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset size in CFFIndex: ".concat(offSize, " ").concat(stream.pos));
    }
    var ret = [];
    var startPos = stream.pos + (count + 1) * offSize - 1;
    var start = offsetType.decode(stream);
    for (var i = 0; i < count; i++) {
      var end = offsetType.decode(stream);
      if (this.type != null) {
        var pos = stream.pos;
        stream.pos = startPos + start;
        parent.length = end - start;
        ret.push(this.type.decode(stream, parent));
        stream.pos = pos;
      } else {
        ret.push({
          offset: startPos + start,
          length: end - start
        });
      }
      start = end;
    }
    stream.pos = startPos + start;
    return ret;
  };
  _proto.size = function size(arr, parent) {
    var size = 2;
    if (arr.length === 0) {
      return size;
    }
    var type = this.type || new r.Buffer(); // find maximum offset to detminine offset type

    var offset = 1;
    for (var i = 0; i < arr.length; i++) {
      var item = arr[i];
      offset += type.size(item, parent);
    }
    var offsetType;
    if (offset <= 0xff) {
      offsetType = r.uint8;
    } else if (offset <= 0xffff) {
      offsetType = r.uint16;
    } else if (offset <= 0xffffff) {
      offsetType = r.uint24;
    } else if (offset <= 0xffffffff) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset in CFFIndex");
    }
    size += 1 + offsetType.size() * (arr.length + 1);
    size += offset - 1;
    return size;
  };
  _proto.encode = function encode(stream, arr, parent) {
    stream.writeUInt16BE(arr.length);
    if (arr.length === 0) {
      return;
    }
    var type = this.type || new r.Buffer(); // find maximum offset to detminine offset type

    var sizes = [];
    var offset = 1;
    for (var _iterator2 = _createForOfIteratorHelperLoose(arr), _step2; !(_step2 = _iterator2()).done;) {
      var item = _step2.value;
      var s = type.size(item, parent);
      sizes.push(s);
      offset += s;
    }
    var offsetType;
    if (offset <= 0xff) {
      offsetType = r.uint8;
    } else if (offset <= 0xffff) {
      offsetType = r.uint16;
    } else if (offset <= 0xffffff) {
      offsetType = r.uint24;
    } else if (offset <= 0xffffffff) {
      offsetType = r.uint32;
    } else {
      throw new Error("Bad offset in CFFIndex");
    } // write offset size

    stream.writeUInt8(offsetType.size()); // write elements

    offset = 1;
    offsetType.encode(stream, offset);
    for (var _i22 = 0, _sizes = sizes; _i22 < _sizes.length; _i22++) {
      var size = _sizes[_i22];
      offset += size;
      offsetType.encode(stream, offset);
    }
    for (var _iterator3 = _createForOfIteratorHelperLoose(arr), _step3; !(_step3 = _iterator3()).done;) {
      var _item = _step3.value;
      type.encode(stream, _item, parent);
    }
    return;
  };
  return CFFIndex;
}();
var FLOAT_EOF = 0xf;
var FLOAT_LOOKUP = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'E', 'E-', null, '-'];
var FLOAT_ENCODE_LOOKUP = {
  '.': 10,
  'E': 11,
  'E-': 12,
  '-': 14
};
var CFFOperand = /*#__PURE__*/function () {
  function CFFOperand() {}
  CFFOperand.decode = function decode(stream, value) {
    if (32 <= value && value <= 246) {
      return value - 139;
    }
    if (247 <= value && value <= 250) {
      return (value - 247) * 256 + stream.readUInt8() + 108;
    }
    if (251 <= value && value <= 254) {
      return -(value - 251) * 256 - stream.readUInt8() - 108;
    }
    if (value === 28) {
      return stream.readInt16BE();
    }
    if (value === 29) {
      return stream.readInt32BE();
    }
    if (value === 30) {
      var str = '';
      while (true) {
        var b = stream.readUInt8();
        var n1 = b >> 4;
        if (n1 === FLOAT_EOF) {
          break;
        }
        str += FLOAT_LOOKUP[n1];
        var n2 = b & 15;
        if (n2 === FLOAT_EOF) {
          break;
        }
        str += FLOAT_LOOKUP[n2];
      }
      return parseFloat(str);
    }
    return null;
  };
  CFFOperand.size = function size(value) {
    // if the value needs to be forced to the largest size (32 bit)
    // e.g. for unknown pointers, set to 32768
    if (value.forceLarge) {
      value = 32768;
    }
    if ((value | 0) !== value) {
      // floating point
      var str = '' + value;
      return 1 + Math.ceil((str.length + 1) / 2);
    } else if (-107 <= value && value <= 107) {
      return 1;
    } else if (108 <= value && value <= 1131 || -1131 <= value && value <= -108) {
      return 2;
    } else if (-32768 <= value && value <= 32767) {
      return 3;
    } else {
      return 5;
    }
  };
  CFFOperand.encode = function encode(stream, value) {
    // if the value needs to be forced to the largest size (32 bit)
    // e.g. for unknown pointers, save the old value and set to 32768
    var val = Number(value);
    if (value.forceLarge) {
      stream.writeUInt8(29);
      return stream.writeInt32BE(val);
    } else if ((val | 0) !== val) {
      // floating point
      stream.writeUInt8(30);
      var str = '' + val;
      for (var i = 0; i < str.length; i += 2) {
        var c1 = str[i];
        var n1 = FLOAT_ENCODE_LOOKUP[c1] || +c1;
        if (i === str.length - 1) {
          var n2 = FLOAT_EOF;
        } else {
          var c2 = str[i + 1];
          var n2 = FLOAT_ENCODE_LOOKUP[c2] || +c2;
        }
        stream.writeUInt8(n1 << 4 | n2 & 15);
      }
      if (n2 !== FLOAT_EOF) {
        return stream.writeUInt8(FLOAT_EOF << 4);
      }
    } else if (-107 <= val && val <= 107) {
      return stream.writeUInt8(val + 139);
    } else if (108 <= val && val <= 1131) {
      val -= 108;
      stream.writeUInt8((val >> 8) + 247);
      return stream.writeUInt8(val & 0xff);
    } else if (-1131 <= val && val <= -108) {
      val = -val - 108;
      stream.writeUInt8((val >> 8) + 251);
      return stream.writeUInt8(val & 0xff);
    } else if (-32768 <= val && val <= 32767) {
      stream.writeUInt8(28);
      return stream.writeInt16BE(val);
    } else {
      stream.writeUInt8(29);
      return stream.writeInt32BE(val);
    }
  };
  return CFFOperand;
}();
var CFFDict = /*#__PURE__*/function () {
  function CFFDict(ops) {
    if (ops === void 0) {
      ops = [];
    }
    this.ops = ops;
    this.fields = {};
    for (var _iterator4 = _createForOfIteratorHelperLoose(ops), _step4; !(_step4 = _iterator4()).done;) {
      var field = _step4.value;
      var key = Array.isArray(field[0]) ? field[0][0] << 8 | field[0][1] : field[0];
      this.fields[key] = field;
    }
  }
  var _proto2 = CFFDict.prototype;
  _proto2.decodeOperands = function decodeOperands(type, stream, ret, operands) {
    var _this = this;
    if (Array.isArray(type)) {
      return operands.map(function (op, i) {
        return _this.decodeOperands(type[i], stream, ret, [op]);
      });
    } else if (type.decode != null) {
      return type.decode(stream, ret, operands);
    } else {
      switch (type) {
        case 'number':
        case 'offset':
        case 'sid':
          return operands[0];
        case 'boolean':
          return !!operands[0];
        default:
          return operands;
      }
    }
  };
  _proto2.encodeOperands = function encodeOperands(type, stream, ctx, operands) {
    var _this2 = this;
    if (Array.isArray(type)) {
      return operands.map(function (op, i) {
        return _this2.encodeOperands(type[i], stream, ctx, op)[0];
      });
    } else if (type.encode != null) {
      return type.encode(stream, operands, ctx);
    } else if (typeof operands === 'number') {
      return [operands];
    } else if (typeof operands === 'boolean') {
      return [+operands];
    } else if (Array.isArray(operands)) {
      return operands;
    } else {
      return [operands];
    }
  };
  _proto2.decode = function decode(stream, parent) {
    var end = stream.pos + parent.length;
    var ret = {};
    var operands = []; // define hidden properties

    Object.defineProperties(ret, {
      parent: {
        value: parent
      },
      _startOffset: {
        value: stream.pos
      }
    }); // fill in defaults

    for (var key in this.fields) {
      var field = this.fields[key];
      ret[field[1]] = field[3];
    }
    while (stream.pos < end) {
      var b = stream.readUInt8();
      if (b < 28) {
        if (b === 12) {
          b = b << 8 | stream.readUInt8();
        }
        var _field = this.fields[b];
        if (!_field) {
          throw new Error("Unknown operator ".concat(b));
        }
        var val = this.decodeOperands(_field[2], stream, ret, operands);
        if (val != null) {
          if (val instanceof utils.PropertyDescriptor) {
            Object.defineProperty(ret, _field[1], val);
          } else {
            ret[_field[1]] = val;
          }
        }
        operands = [];
      } else {
        operands.push(CFFOperand.decode(stream, b));
      }
    }
    return ret;
  };
  _proto2.size = function size(dict, parent, includePointers) {
    if (includePointers === void 0) {
      includePointers = true;
    }
    var ctx = {
      parent: parent,
      val: dict,
      pointerSize: 0,
      startOffset: parent.startOffset || 0
    };
    var len = 0;
    for (var k in this.fields) {
      var field = this.fields[k];
      var val = dict[field[1]];
      if (val == null || isEqual(val, field[3])) {
        continue;
      }
      var operands = this.encodeOperands(field[2], null, ctx, val);
      for (var _iterator5 = _createForOfIteratorHelperLoose(operands), _step5; !(_step5 = _iterator5()).done;) {
        var op = _step5.value;
        len += CFFOperand.size(op);
      }
      var key = Array.isArray(field[0]) ? field[0] : [field[0]];
      len += key.length;
    }
    if (includePointers) {
      len += ctx.pointerSize;
    }
    return len;
  };
  _proto2.encode = function encode(stream, dict, parent) {
    var ctx = {
      pointers: [],
      startOffset: stream.pos,
      parent: parent,
      val: dict,
      pointerSize: 0
    };
    ctx.pointerOffset = stream.pos + this.size(dict, ctx, false);
    for (var _iterator6 = _createForOfIteratorHelperLoose(this.ops), _step6; !(_step6 = _iterator6()).done;) {
      var field = _step6.value;
      var val = dict[field[1]];
      if (val == null || isEqual(val, field[3])) {
        continue;
      }
      var operands = this.encodeOperands(field[2], stream, ctx, val);
      for (var _iterator7 = _createForOfIteratorHelperLoose(operands), _step7; !(_step7 = _iterator7()).done;) {
        var op = _step7.value;
        CFFOperand.encode(stream, op);
      }
      var key = Array.isArray(field[0]) ? field[0] : [field[0]];
      for (var _iterator8 = _createForOfIteratorHelperLoose(key), _step8; !(_step8 = _iterator8()).done;) {
        var _op = _step8.value;
        stream.writeUInt8(_op);
      }
    }
    var i = 0;
    while (i < ctx.pointers.length) {
      var ptr = ctx.pointers[i++];
      ptr.type.encode(stream, ptr.val, ptr.parent);
    }
    return;
  };
  return CFFDict;
}();
var CFFPointer = /*#__PURE__*/function (_r$Pointer) {
  function CFFPointer(type, options) {
    if (options === void 0) {
      options = {};
    }
    if (options.type == null) {
      options.type = 'global';
    }
    return _r$Pointer.call(this, null, type, options) || this;
  }
  _inheritsLoose(CFFPointer, _r$Pointer);
  var _proto3 = CFFPointer.prototype;
  _proto3.decode = function decode(stream, parent, operands) {
    this.offsetType = {
      decode: function decode() {
        return operands[0];
      }
    };
    return _r$Pointer.prototype.decode.call(this, stream, parent, operands);
  };
  _proto3.encode = function encode(stream, value, ctx) {
    if (!stream) {
      // compute the size (so ctx.pointerSize is correct)
      this.offsetType = {
        size: function size() {
          return 0;
        }
      };
      this.size(value, ctx);
      return [new Ptr(0)];
    }
    var ptr = null;
    this.offsetType = {
      encode: function encode(stream, val) {
        return ptr = val;
      }
    };
    _r$Pointer.prototype.encode.call(this, stream, value, ctx);
    return [new Ptr(ptr)];
  };
  return CFFPointer;
}(r.Pointer);
var Ptr = /*#__PURE__*/function () {
  function Ptr(val) {
    this.val = val;
    this.forceLarge = true;
  }
  var _proto4 = Ptr.prototype;
  _proto4.valueOf = function valueOf() {
    return this.val;
  };
  return Ptr;
}();
var CFFBlendOp = /*#__PURE__*/function () {
  function CFFBlendOp() {}
  CFFBlendOp.decode = function decode(stream, parent, operands) {
    var numBlends = operands.pop(); // TODO: actually blend. For now just consume the deltas
    // since we don't use any of the values anyway.

    while (operands.length > numBlends) {
      operands.pop();
    }
  };
  return CFFBlendOp;
}();
var CFFPrivateDict = new CFFDict([
// key       name                    type                                          default
[6, 'BlueValues', 'delta', null], [7, 'OtherBlues', 'delta', null], [8, 'FamilyBlues', 'delta', null], [9, 'FamilyOtherBlues', 'delta', null], [[12, 9], 'BlueScale', 'number', 0.039625], [[12, 10], 'BlueShift', 'number', 7], [[12, 11], 'BlueFuzz', 'number', 1], [10, 'StdHW', 'number', null], [11, 'StdVW', 'number', null], [[12, 12], 'StemSnapH', 'delta', null], [[12, 13], 'StemSnapV', 'delta', null], [[12, 14], 'ForceBold', 'boolean', false], [[12, 17], 'LanguageGroup', 'number', 0], [[12, 18], 'ExpansionFactor', 'number', 0.06], [[12, 19], 'initialRandomSeed', 'number', 0], [20, 'defaultWidthX', 'number', 0], [21, 'nominalWidthX', 'number', 0], [22, 'vsindex', 'number', 0], [23, 'blend', CFFBlendOp, null], [19, 'Subrs', new CFFPointer(new CFFIndex(), {
  type: 'local'
}), null]]);

// Automatically generated from Appendix A of the CFF specification; do
// not edit. Length should be 391.
var standardStrings = [".notdef", "space", "exclam", "quotedbl", "numbersign", "dollar", "percent", "ampersand", "quoteright", "parenleft", "parenright", "asterisk", "plus", "comma", "hyphen", "period", "slash", "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "colon", "semicolon", "less", "equal", "greater", "question", "at", "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "bracketleft", "backslash", "bracketright", "asciicircum", "underscore", "quoteleft", "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "braceleft", "bar", "braceright", "asciitilde", "exclamdown", "cent", "sterling", "fraction", "yen", "florin", "section", "currency", "quotesingle", "quotedblleft", "guillemotleft", "guilsinglleft", "guilsinglright", "fi", "fl", "endash", "dagger", "daggerdbl", "periodcentered", "paragraph", "bullet", "quotesinglbase", "quotedblbase", "quotedblright", "guillemotright", "ellipsis", "perthousand", "questiondown", "grave", "acute", "circumflex", "tilde", "macron", "breve", "dotaccent", "dieresis", "ring", "cedilla", "hungarumlaut", "ogonek", "caron", "emdash", "AE", "ordfeminine", "Lslash", "Oslash", "OE", "ordmasculine", "ae", "dotlessi", "lslash", "oslash", "oe", "germandbls", "onesuperior", "logicalnot", "mu", "trademark", "Eth", "onehalf", "plusminus", "Thorn", "onequarter", "divide", "brokenbar", "degree", "thorn", "threequarters", "twosuperior", "registered", "minus", "eth", "multiply", "threesuperior", "copyright", "Aacute", "Acircumflex", "Adieresis", "Agrave", "Aring", "Atilde", "Ccedilla", "Eacute", "Ecircumflex", "Edieresis", "Egrave", "Iacute", "Icircumflex", "Idieresis", "Igrave", "Ntilde", "Oacute", "Ocircumflex", "Odieresis", "Ograve", "Otilde", "Scaron", "Uacute", "Ucircumflex", "Udieresis", "Ugrave", "Yacute", "Ydieresis", "Zcaron", "aacute", "acircumflex", "adieresis", "agrave", "aring", "atilde", "ccedilla", "eacute", "ecircumflex", "edieresis", "egrave", "iacute", "icircumflex", "idieresis", "igrave", "ntilde", "oacute", "ocircumflex", "odieresis", "ograve", "otilde", "scaron", "uacute", "ucircumflex", "udieresis", "ugrave", "yacute", "ydieresis", "zcaron", "exclamsmall", "Hungarumlautsmall", "dollaroldstyle", "dollarsuperior", "ampersandsmall", "Acutesmall", "parenleftsuperior", "parenrightsuperior", "twodotenleader", "onedotenleader", "zerooldstyle", "oneoldstyle", "twooldstyle", "threeoldstyle", "fouroldstyle", "fiveoldstyle", "sixoldstyle", "sevenoldstyle", "eightoldstyle", "nineoldstyle", "commasuperior", "threequartersemdash", "periodsuperior", "questionsmall", "asuperior", "bsuperior", "centsuperior", "dsuperior", "esuperior", "isuperior", "lsuperior", "msuperior", "nsuperior", "osuperior", "rsuperior", "ssuperior", "tsuperior", "ff", "ffi", "ffl", "parenleftinferior", "parenrightinferior", "Circumflexsmall", "hyphensuperior", "Gravesmall", "Asmall", "Bsmall", "Csmall", "Dsmall", "Esmall", "Fsmall", "Gsmall", "Hsmall", "Ismall", "Jsmall", "Ksmall", "Lsmall", "Msmall", "Nsmall", "Osmall", "Psmall", "Qsmall", "Rsmall", "Ssmall", "Tsmall", "Usmall", "Vsmall", "Wsmall", "Xsmall", "Ysmall", "Zsmall", "colonmonetary", "onefitted", "rupiah", "Tildesmall", "exclamdownsmall", "centoldstyle", "Lslashsmall", "Scaronsmall", "Zcaronsmall", "Dieresissmall", "Brevesmall", "Caronsmall", "Dotaccentsmall", "Macronsmall", "figuredash", "hypheninferior", "Ogoneksmall", "Ringsmall", "Cedillasmall", "questiondownsmall", "oneeighth", "threeeighths", "fiveeighths", "seveneighths", "onethird", "twothirds", "zerosuperior", "foursuperior", "fivesuperior", "sixsuperior", "sevensuperior", "eightsuperior", "ninesuperior", "zeroinferior", "oneinferior", "twoinferior", "threeinferior", "fourinferior", "fiveinferior", "sixinferior", "seveninferior", "eightinferior", "nineinferior", "centinferior", "dollarinferior", "periodinferior", "commainferior", "Agravesmall", "Aacutesmall", "Acircumflexsmall", "Atildesmall", "Adieresissmall", "Aringsmall", "AEsmall", "Ccedillasmall", "Egravesmall", "Eacutesmall", "Ecircumflexsmall", "Edieresissmall", "Igravesmall", "Iacutesmall", "Icircumflexsmall", "Idieresissmall", "Ethsmall", "Ntildesmall", "Ogravesmall", "Oacutesmall", "Ocircumflexsmall", "Otildesmall", "Odieresissmall", "OEsmall", "Oslashsmall", "Ugravesmall", "Uacutesmall", "Ucircumflexsmall", "Udieresissmall", "Yacutesmall", "Thornsmall", "Ydieresissmall", "001.000", "001.001", "001.002", "001.003", "Black", "Bold", "Book", "Light", "Medium", "Regular", "Roman", "Semibold"];
var StandardEncoding = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quoteright', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'quoteleft', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'exclamdown', 'cent', 'sterling', 'fraction', 'yen', 'florin', 'section', 'currency', 'quotesingle', 'quotedblleft', 'guillemotleft', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', '', 'endash', 'dagger', 'daggerdbl', 'periodcentered', '', 'paragraph', 'bullet', 'quotesinglbase', 'quotedblbase', 'quotedblright', 'guillemotright', 'ellipsis', 'perthousand', '', 'questiondown', '', 'grave', 'acute', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'dieresis', '', 'ring', 'cedilla', '', 'hungarumlaut', 'ogonek', 'caron', 'emdash', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'AE', '', 'ordfeminine', '', '', '', '', 'Lslash', 'Oslash', 'OE', 'ordmasculine', '', '', '', '', '', 'ae', '', '', '', 'dotlessi', '', '', 'lslash', 'oslash', 'oe', 'germandbls'];
var ExpertEncoding = ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'space', 'exclamsmall', 'Hungarumlautsmall', '', 'dollaroldstyle', 'dollarsuperior', 'ampersandsmall', 'Acutesmall', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'questionsmall', '', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', '', '', 'isuperior', '', '', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', '', '', 'rsuperior', 'ssuperior', 'tsuperior', '', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', '', 'parenrightinferior', 'Circumflexsmall', 'hyphensuperior', 'Gravesmall', 'Asmall', 'Bsmall', 'Csmall', 'Dsmall', 'Esmall', 'Fsmall', 'Gsmall', 'Hsmall', 'Ismall', 'Jsmall', 'Ksmall', 'Lsmall', 'Msmall', 'Nsmall', 'Osmall', 'Psmall', 'Qsmall', 'Rsmall', 'Ssmall', 'Tsmall', 'Usmall', 'Vsmall', 'Wsmall', 'Xsmall', 'Ysmall', 'Zsmall', 'colonmonetary', 'onefitted', 'rupiah', 'Tildesmall', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'exclamdownsmall', 'centoldstyle', 'Lslashsmall', '', '', 'Scaronsmall', 'Zcaronsmall', 'Dieresissmall', 'Brevesmall', 'Caronsmall', '', 'Dotaccentsmall', '', '', 'Macronsmall', '', '', 'figuredash', 'hypheninferior', '', '', 'Ogoneksmall', 'Ringsmall', 'Cedillasmall', '', '', '', 'onequarter', 'onehalf', 'threequarters', 'questiondownsmall', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', '', '', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior', 'Agravesmall', 'Aacutesmall', 'Acircumflexsmall', 'Atildesmall', 'Adieresissmall', 'Aringsmall', 'AEsmall', 'Ccedillasmall', 'Egravesmall', 'Eacutesmall', 'Ecircumflexsmall', 'Edieresissmall', 'Igravesmall', 'Iacutesmall', 'Icircumflexsmall', 'Idieresissmall', 'Ethsmall', 'Ntildesmall', 'Ogravesmall', 'Oacutesmall', 'Ocircumflexsmall', 'Otildesmall', 'Odieresissmall', 'OEsmall', 'Oslashsmall', 'Ugravesmall', 'Uacutesmall', 'Ucircumflexsmall', 'Udieresissmall', 'Yacutesmall', 'Thornsmall', 'Ydieresissmall'];
var ISOAdobeCharset = ['.notdef', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quoteright', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'quoteleft', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', 'exclamdown', 'cent', 'sterling', 'fraction', 'yen', 'florin', 'section', 'currency', 'quotesingle', 'quotedblleft', 'guillemotleft', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', 'endash', 'dagger', 'daggerdbl', 'periodcentered', 'paragraph', 'bullet', 'quotesinglbase', 'quotedblbase', 'quotedblright', 'guillemotright', 'ellipsis', 'perthousand', 'questiondown', 'grave', 'acute', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'dieresis', 'ring', 'cedilla', 'hungarumlaut', 'ogonek', 'caron', 'emdash', 'AE', 'ordfeminine', 'Lslash', 'Oslash', 'OE', 'ordmasculine', 'ae', 'dotlessi', 'lslash', 'oslash', 'oe', 'germandbls', 'onesuperior', 'logicalnot', 'mu', 'trademark', 'Eth', 'onehalf', 'plusminus', 'Thorn', 'onequarter', 'divide', 'brokenbar', 'degree', 'thorn', 'threequarters', 'twosuperior', 'registered', 'minus', 'eth', 'multiply', 'threesuperior', 'copyright', 'Aacute', 'Acircumflex', 'Adieresis', 'Agrave', 'Aring', 'Atilde', 'Ccedilla', 'Eacute', 'Ecircumflex', 'Edieresis', 'Egrave', 'Iacute', 'Icircumflex', 'Idieresis', 'Igrave', 'Ntilde', 'Oacute', 'Ocircumflex', 'Odieresis', 'Ograve', 'Otilde', 'Scaron', 'Uacute', 'Ucircumflex', 'Udieresis', 'Ugrave', 'Yacute', 'Ydieresis', 'Zcaron', 'aacute', 'acircumflex', 'adieresis', 'agrave', 'aring', 'atilde', 'ccedilla', 'eacute', 'ecircumflex', 'edieresis', 'egrave', 'iacute', 'icircumflex', 'idieresis', 'igrave', 'ntilde', 'oacute', 'ocircumflex', 'odieresis', 'ograve', 'otilde', 'scaron', 'uacute', 'ucircumflex', 'udieresis', 'ugrave', 'yacute', 'ydieresis', 'zcaron'];
var ExpertCharset = ['.notdef', 'space', 'exclamsmall', 'Hungarumlautsmall', 'dollaroldstyle', 'dollarsuperior', 'ampersandsmall', 'Acutesmall', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'questionsmall', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', 'isuperior', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', 'rsuperior', 'ssuperior', 'tsuperior', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', 'parenrightinferior', 'Circumflexsmall', 'hyphensuperior', 'Gravesmall', 'Asmall', 'Bsmall', 'Csmall', 'Dsmall', 'Esmall', 'Fsmall', 'Gsmall', 'Hsmall', 'Ismall', 'Jsmall', 'Ksmall', 'Lsmall', 'Msmall', 'Nsmall', 'Osmall', 'Psmall', 'Qsmall', 'Rsmall', 'Ssmall', 'Tsmall', 'Usmall', 'Vsmall', 'Wsmall', 'Xsmall', 'Ysmall', 'Zsmall', 'colonmonetary', 'onefitted', 'rupiah', 'Tildesmall', 'exclamdownsmall', 'centoldstyle', 'Lslashsmall', 'Scaronsmall', 'Zcaronsmall', 'Dieresissmall', 'Brevesmall', 'Caronsmall', 'Dotaccentsmall', 'Macronsmall', 'figuredash', 'hypheninferior', 'Ogoneksmall', 'Ringsmall', 'Cedillasmall', 'onequarter', 'onehalf', 'threequarters', 'questiondownsmall', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior', 'Agravesmall', 'Aacutesmall', 'Acircumflexsmall', 'Atildesmall', 'Adieresissmall', 'Aringsmall', 'AEsmall', 'Ccedillasmall', 'Egravesmall', 'Eacutesmall', 'Ecircumflexsmall', 'Edieresissmall', 'Igravesmall', 'Iacutesmall', 'Icircumflexsmall', 'Idieresissmall', 'Ethsmall', 'Ntildesmall', 'Ogravesmall', 'Oacutesmall', 'Ocircumflexsmall', 'Otildesmall', 'Odieresissmall', 'OEsmall', 'Oslashsmall', 'Ugravesmall', 'Uacutesmall', 'Ucircumflexsmall', 'Udieresissmall', 'Yacutesmall', 'Thornsmall', 'Ydieresissmall'];
var ExpertSubsetCharset = ['.notdef', 'space', 'dollaroldstyle', 'dollarsuperior', 'parenleftsuperior', 'parenrightsuperior', 'twodotenleader', 'onedotenleader', 'comma', 'hyphen', 'period', 'fraction', 'zerooldstyle', 'oneoldstyle', 'twooldstyle', 'threeoldstyle', 'fouroldstyle', 'fiveoldstyle', 'sixoldstyle', 'sevenoldstyle', 'eightoldstyle', 'nineoldstyle', 'colon', 'semicolon', 'commasuperior', 'threequartersemdash', 'periodsuperior', 'asuperior', 'bsuperior', 'centsuperior', 'dsuperior', 'esuperior', 'isuperior', 'lsuperior', 'msuperior', 'nsuperior', 'osuperior', 'rsuperior', 'ssuperior', 'tsuperior', 'ff', 'fi', 'fl', 'ffi', 'ffl', 'parenleftinferior', 'parenrightinferior', 'hyphensuperior', 'colonmonetary', 'onefitted', 'rupiah', 'centoldstyle', 'figuredash', 'hypheninferior', 'onequarter', 'onehalf', 'threequarters', 'oneeighth', 'threeeighths', 'fiveeighths', 'seveneighths', 'onethird', 'twothirds', 'zerosuperior', 'onesuperior', 'twosuperior', 'threesuperior', 'foursuperior', 'fivesuperior', 'sixsuperior', 'sevensuperior', 'eightsuperior', 'ninesuperior', 'zeroinferior', 'oneinferior', 'twoinferior', 'threeinferior', 'fourinferior', 'fiveinferior', 'sixinferior', 'seveninferior', 'eightinferior', 'nineinferior', 'centinferior', 'dollarinferior', 'periodinferior', 'commainferior'];

// Scripts and Languages #
//########################

var LangSysTable = new r.Struct({
  reserved: new r.Reserved(r.uint16),
  reqFeatureIndex: r.uint16,
  featureCount: r.uint16,
  featureIndexes: new r.Array(r.uint16, 'featureCount')
});
var LangSysRecord = new r.Struct({
  tag: new r.String(4),
  langSys: new r.Pointer(r.uint16, LangSysTable, {
    type: 'parent'
  })
});
var Script = new r.Struct({
  defaultLangSys: new r.Pointer(r.uint16, LangSysTable),
  count: r.uint16,
  langSysRecords: new r.Array(LangSysRecord, 'count')
});
var ScriptRecord = new r.Struct({
  tag: new r.String(4),
  script: new r.Pointer(r.uint16, Script, {
    type: 'parent'
  })
});
var ScriptList = new r.Array(ScriptRecord, r.uint16); //#######################
// Features and Lookups #
//#######################

var Feature = new r.Struct({
  featureParams: r.uint16,
  // pointer
  lookupCount: r.uint16,
  lookupListIndexes: new r.Array(r.uint16, 'lookupCount')
});
var FeatureRecord = new r.Struct({
  tag: new r.String(4),
  feature: new r.Pointer(r.uint16, Feature, {
    type: 'parent'
  })
});
var FeatureList = new r.Array(FeatureRecord, r.uint16);
var LookupFlags = new r.Struct({
  markAttachmentType: r.uint8,
  flags: new r.Bitfield(r.uint8, ['rightToLeft', 'ignoreBaseGlyphs', 'ignoreLigatures', 'ignoreMarks', 'useMarkFilteringSet'])
});
function LookupList(SubTable) {
  var Lookup = new r.Struct({
    lookupType: r.uint16,
    flags: LookupFlags,
    subTableCount: r.uint16,
    subTables: new r.Array(new r.Pointer(r.uint16, SubTable), 'subTableCount'),
    markFilteringSet: new r.Optional(r.uint16, function (t) {
      return t.flags.flags.useMarkFilteringSet;
    })
  });
  return new r.LazyArray(new r.Pointer(r.uint16, Lookup), r.uint16);
} //#################
// Coverage Table #
//#################

var RangeRecord = new r.Struct({
  start: r.uint16,
  end: r.uint16,
  startCoverageIndex: r.uint16
});
var Coverage = new r.VersionedStruct(r.uint16, {
  1: {
    glyphCount: r.uint16,
    glyphs: new r.Array(r.uint16, 'glyphCount')
  },
  2: {
    rangeCount: r.uint16,
    rangeRecords: new r.Array(RangeRecord, 'rangeCount')
  }
}); //#########################
// Class Definition Table #
//#########################

var ClassRangeRecord = new r.Struct({
  start: r.uint16,
  end: r.uint16,
  class: r.uint16
});
var ClassDef = new r.VersionedStruct(r.uint16, {
  1: {
    // Class array
    startGlyph: r.uint16,
    glyphCount: r.uint16,
    classValueArray: new r.Array(r.uint16, 'glyphCount')
  },
  2: {
    // Class ranges
    classRangeCount: r.uint16,
    classRangeRecord: new r.Array(ClassRangeRecord, 'classRangeCount')
  }
}); //###############
// Device Table #
//###############

var Device = new r.Struct({
  a: r.uint16,
  // startSize for hinting Device, outerIndex for VariationIndex
  b: r.uint16,
  // endSize for Device, innerIndex for VariationIndex
  deltaFormat: r.uint16
}); //#############################################
// Contextual Substitution/Positioning Tables #
//#############################################

var LookupRecord = new r.Struct({
  sequenceIndex: r.uint16,
  lookupListIndex: r.uint16
});
var Rule = new r.Struct({
  glyphCount: r.uint16,
  lookupCount: r.uint16,
  input: new r.Array(r.uint16, function (t) {
    return t.glyphCount - 1;
  }),
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});
var RuleSet = new r.Array(new r.Pointer(r.uint16, Rule), r.uint16);
var ClassRule = new r.Struct({
  glyphCount: r.uint16,
  lookupCount: r.uint16,
  classes: new r.Array(r.uint16, function (t) {
    return t.glyphCount - 1;
  }),
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});
var ClassSet = new r.Array(new r.Pointer(r.uint16, ClassRule), r.uint16);
var Context = new r.VersionedStruct(r.uint16, {
  1: {
    // Simple context
    coverage: new r.Pointer(r.uint16, Coverage),
    ruleSetCount: r.uint16,
    ruleSets: new r.Array(new r.Pointer(r.uint16, RuleSet), 'ruleSetCount')
  },
  2: {
    // Class-based context
    coverage: new r.Pointer(r.uint16, Coverage),
    classDef: new r.Pointer(r.uint16, ClassDef),
    classSetCnt: r.uint16,
    classSet: new r.Array(new r.Pointer(r.uint16, ClassSet), 'classSetCnt')
  },
  3: {
    glyphCount: r.uint16,
    lookupCount: r.uint16,
    coverages: new r.Array(new r.Pointer(r.uint16, Coverage), 'glyphCount'),
    lookupRecords: new r.Array(LookupRecord, 'lookupCount')
  }
}); //######################################################
// Chaining Contextual Substitution/Positioning Tables #
//######################################################

var ChainRule = new r.Struct({
  backtrackGlyphCount: r.uint16,
  backtrack: new r.Array(r.uint16, 'backtrackGlyphCount'),
  inputGlyphCount: r.uint16,
  input: new r.Array(r.uint16, function (t) {
    return t.inputGlyphCount - 1;
  }),
  lookaheadGlyphCount: r.uint16,
  lookahead: new r.Array(r.uint16, 'lookaheadGlyphCount'),
  lookupCount: r.uint16,
  lookupRecords: new r.Array(LookupRecord, 'lookupCount')
});
var ChainRuleSet = new r.Array(new r.Pointer(r.uint16, ChainRule), r.uint16);
var ChainingContext = new r.VersionedStruct(r.uint16, {
  1: {
    // Simple context glyph substitution
    coverage: new r.Pointer(r.uint16, Coverage),
    chainCount: r.uint16,
    chainRuleSets: new r.Array(new r.Pointer(r.uint16, ChainRuleSet), 'chainCount')
  },
  2: {
    // Class-based chaining context
    coverage: new r.Pointer(r.uint16, Coverage),
    backtrackClassDef: new r.Pointer(r.uint16, ClassDef),
    inputClassDef: new r.Pointer(r.uint16, ClassDef),
    lookaheadClassDef: new r.Pointer(r.uint16, ClassDef),
    chainCount: r.uint16,
    chainClassSet: new r.Array(new r.Pointer(r.uint16, ChainRuleSet), 'chainCount')
  },
  3: {
    // Coverage-based chaining context
    backtrackGlyphCount: r.uint16,
    backtrackCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'backtrackGlyphCount'),
    inputGlyphCount: r.uint16,
    inputCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'inputGlyphCount'),
    lookaheadGlyphCount: r.uint16,
    lookaheadCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'lookaheadGlyphCount'),
    lookupCount: r.uint16,
    lookupRecords: new r.Array(LookupRecord, 'lookupCount')
  }
});

/*******************
 * Variation Store *
 *******************/

var F2DOT14 = new r.Fixed(16, 'BE', 14);
var RegionAxisCoordinates = new r.Struct({
  startCoord: F2DOT14,
  peakCoord: F2DOT14,
  endCoord: F2DOT14
});
var VariationRegionList = new r.Struct({
  axisCount: r.uint16,
  regionCount: r.uint16,
  variationRegions: new r.Array(new r.Array(RegionAxisCoordinates, 'axisCount'), 'regionCount')
});
var DeltaSet = new r.Struct({
  shortDeltas: new r.Array(r.int16, function (t) {
    return t.parent.shortDeltaCount;
  }),
  regionDeltas: new r.Array(r.int8, function (t) {
    return t.parent.regionIndexCount - t.parent.shortDeltaCount;
  }),
  deltas: function deltas(t) {
    return t.shortDeltas.concat(t.regionDeltas);
  }
});
var ItemVariationData = new r.Struct({
  itemCount: r.uint16,
  shortDeltaCount: r.uint16,
  regionIndexCount: r.uint16,
  regionIndexes: new r.Array(r.uint16, 'regionIndexCount'),
  deltaSets: new r.Array(DeltaSet, 'itemCount')
});
var ItemVariationStore = new r.Struct({
  format: r.uint16,
  variationRegionList: new r.Pointer(r.uint32, VariationRegionList),
  variationDataCount: r.uint16,
  itemVariationData: new r.Array(new r.Pointer(r.uint32, ItemVariationData), 'variationDataCount')
});
/**********************
 * Feature Variations *
 **********************/

var ConditionTable = new r.VersionedStruct(r.uint16, {
  1: (_ = {
    axisIndex: r.uint16
  }, _["axisIndex"] = r.uint16, _.filterRangeMinValue = F2DOT14, _.filterRangeMaxValue = F2DOT14, _)
});
var ConditionSet = new r.Struct({
  conditionCount: r.uint16,
  conditionTable: new r.Array(new r.Pointer(r.uint32, ConditionTable), 'conditionCount')
});
var FeatureTableSubstitutionRecord = new r.Struct({
  featureIndex: r.uint16,
  alternateFeatureTable: new r.Pointer(r.uint32, Feature, {
    type: 'parent'
  })
});
var FeatureTableSubstitution = new r.Struct({
  version: r.fixed32,
  substitutionCount: r.uint16,
  substitutions: new r.Array(FeatureTableSubstitutionRecord, 'substitutionCount')
});
var FeatureVariationRecord = new r.Struct({
  conditionSet: new r.Pointer(r.uint32, ConditionSet, {
    type: 'parent'
  }),
  featureTableSubstitution: new r.Pointer(r.uint32, FeatureTableSubstitution, {
    type: 'parent'
  })
});
var FeatureVariations = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  featureVariationRecordCount: r.uint32,
  featureVariationRecords: new r.Array(FeatureVariationRecord, 'featureVariationRecordCount')
});

// otherwise delegates to the provided type.
var PredefinedOp = /*#__PURE__*/function () {
  function PredefinedOp(predefinedOps, type) {
    this.predefinedOps = predefinedOps;
    this.type = type;
  }
  var _proto5 = PredefinedOp.prototype;
  _proto5.decode = function decode(stream, parent, operands) {
    if (this.predefinedOps[operands[0]]) {
      return this.predefinedOps[operands[0]];
    }
    return this.type.decode(stream, parent, operands);
  };
  _proto5.size = function size(value, ctx) {
    return this.type.size(value, ctx);
  };
  _proto5.encode = function encode(stream, value, ctx) {
    var index = this.predefinedOps.indexOf(value);
    if (index !== -1) {
      return index;
    }
    return this.type.encode(stream, value, ctx);
  };
  return PredefinedOp;
}();
var CFFEncodingVersion = /*#__PURE__*/function (_r$Number) {
  function CFFEncodingVersion() {
    return _r$Number.call(this, 'UInt8') || this;
  }
  _inheritsLoose(CFFEncodingVersion, _r$Number);
  var _proto6 = CFFEncodingVersion.prototype;
  _proto6.decode = function decode(stream) {
    return r.uint8.decode(stream) & 0x7f;
  };
  return CFFEncodingVersion;
}(r.Number);
var Range1 = new r.Struct({
  first: r.uint16,
  nLeft: r.uint8
});
var Range2 = new r.Struct({
  first: r.uint16,
  nLeft: r.uint16
});
var CFFCustomEncoding = new r.VersionedStruct(new CFFEncodingVersion(), {
  0: {
    nCodes: r.uint8,
    codes: new r.Array(r.uint8, 'nCodes')
  },
  1: {
    nRanges: r.uint8,
    ranges: new r.Array(Range1, 'nRanges')
  } // TODO: supplement?
});
var CFFEncoding = new PredefinedOp([StandardEncoding, ExpertEncoding], new CFFPointer(CFFCustomEncoding, {
  lazy: true
})); // Decodes an array of ranges until the total
// length is equal to the provided length.
var RangeArray = /*#__PURE__*/function (_r$Array) {
  function RangeArray() {
    return _r$Array.apply(this, arguments) || this;
  }
  _inheritsLoose(RangeArray, _r$Array);
  var _proto7 = RangeArray.prototype;
  _proto7.decode = function decode(stream, parent) {
    var length = utils.resolveLength(this.length, stream, parent);
    var count = 0;
    var res = [];
    while (count < length) {
      var range = this.type.decode(stream, parent);
      range.offset = count;
      count += range.nLeft + 1;
      res.push(range);
    }
    return res;
  };
  return RangeArray;
}(r.Array);
var CFFCustomCharset = new r.VersionedStruct(r.uint8, {
  0: {
    glyphs: new r.Array(r.uint16, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  },
  1: {
    ranges: new RangeArray(Range1, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  },
  2: {
    ranges: new RangeArray(Range2, function (t) {
      return t.parent.CharStrings.length - 1;
    })
  }
});
var CFFCharset = new PredefinedOp([ISOAdobeCharset, ExpertCharset, ExpertSubsetCharset], new CFFPointer(CFFCustomCharset, {
  lazy: true
}));
var FDRange3 = new r.Struct({
  first: r.uint16,
  fd: r.uint8
});
var FDRange4 = new r.Struct({
  first: r.uint32,
  fd: r.uint16
});
var FDSelect = new r.VersionedStruct(r.uint8, {
  0: {
    fds: new r.Array(r.uint8, function (t) {
      return t.parent.CharStrings.length;
    })
  },
  3: {
    nRanges: r.uint16,
    ranges: new r.Array(FDRange3, 'nRanges'),
    sentinel: r.uint16
  },
  4: {
    nRanges: r.uint32,
    ranges: new r.Array(FDRange4, 'nRanges'),
    sentinel: r.uint32
  }
});
var ptr = new CFFPointer(CFFPrivateDict);
var CFFPrivateOp = /*#__PURE__*/function () {
  function CFFPrivateOp() {}
  var _proto8 = CFFPrivateOp.prototype;
  _proto8.decode = function decode(stream, parent, operands) {
    parent.length = operands[0];
    return ptr.decode(stream, parent, [operands[1]]);
  };
  _proto8.size = function size(dict, ctx) {
    return [CFFPrivateDict.size(dict, ctx, false), ptr.size(dict, ctx)[0]];
  };
  _proto8.encode = function encode(stream, dict, ctx) {
    return [CFFPrivateDict.size(dict, ctx, false), ptr.encode(stream, dict, ctx)[0]];
  };
  return CFFPrivateOp;
}();
var FontDict = new CFFDict([
// key       name                   type(s)                                 default
[18, 'Private', new CFFPrivateOp(), null], [[12, 38], 'FontName', 'sid', null], [[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [[12, 5], 'PaintType', 'number', 0]]);
var CFFTopDict = new CFFDict([
// key       name                   type(s)                                 default
[[12, 30], 'ROS', ['sid', 'sid', 'number'], null], [0, 'version', 'sid', null], [1, 'Notice', 'sid', null], [[12, 0], 'Copyright', 'sid', null], [2, 'FullName', 'sid', null], [3, 'FamilyName', 'sid', null], [4, 'Weight', 'sid', null], [[12, 1], 'isFixedPitch', 'boolean', false], [[12, 2], 'ItalicAngle', 'number', 0], [[12, 3], 'UnderlinePosition', 'number', -100], [[12, 4], 'UnderlineThickness', 'number', 50], [[12, 5], 'PaintType', 'number', 0], [[12, 6], 'CharstringType', 'number', 2], [[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [13, 'UniqueID', 'number', null], [5, 'FontBBox', 'array', [0, 0, 0, 0]], [[12, 8], 'StrokeWidth', 'number', 0], [14, 'XUID', 'array', null], [15, 'charset', CFFCharset, ISOAdobeCharset], [16, 'Encoding', CFFEncoding, StandardEncoding], [17, 'CharStrings', new CFFPointer(new CFFIndex()), null], [18, 'Private', new CFFPrivateOp(), null], [[12, 20], 'SyntheticBase', 'number', null], [[12, 21], 'PostScript', 'sid', null], [[12, 22], 'BaseFontName', 'sid', null], [[12, 23], 'BaseFontBlend', 'delta', null],
// CID font specific
[[12, 31], 'CIDFontVersion', 'number', 0], [[12, 32], 'CIDFontRevision', 'number', 0], [[12, 33], 'CIDFontType', 'number', 0], [[12, 34], 'CIDCount', 'number', 8720], [[12, 35], 'UIDBase', 'number', null], [[12, 37], 'FDSelect', new CFFPointer(FDSelect), null], [[12, 36], 'FDArray', new CFFPointer(new CFFIndex(FontDict)), null], [[12, 38], 'FontName', 'sid', null]]);
var VariationStore = new r.Struct({
  length: r.uint16,
  itemVariationStore: ItemVariationStore
});
var CFF2TopDict = new CFFDict([[[12, 7], 'FontMatrix', 'array', [0.001, 0, 0, 0.001, 0, 0]], [17, 'CharStrings', new CFFPointer(new CFFIndex()), null], [[12, 37], 'FDSelect', new CFFPointer(FDSelect), null], [[12, 36], 'FDArray', new CFFPointer(new CFFIndex(FontDict)), null], [24, 'vstore', new CFFPointer(VariationStore), null], [25, 'maxstack', 'number', 193]]);
var CFFTop = new r.VersionedStruct(r.fixed16, {
  1: {
    hdrSize: r.uint8,
    offSize: r.uint8,
    nameIndex: new CFFIndex(new r.String('length')),
    topDictIndex: new CFFIndex(CFFTopDict),
    stringIndex: new CFFIndex(new r.String('length')),
    globalSubrIndex: new CFFIndex()
  },
  2: {
    hdrSize: r.uint8,
    length: r.uint16,
    topDict: CFF2TopDict,
    globalSubrIndex: new CFFIndex()
  }
});
var CFFFont = /*#__PURE__*/function () {
  function CFFFont(stream) {
    this.stream = stream;
    this.decode();
  }
  CFFFont.decode = function decode(stream) {
    return new CFFFont(stream);
  };
  var _proto9 = CFFFont.prototype;
  _proto9.decode = function decode() {
    var start = this.stream.pos;
    var top = CFFTop.decode(this.stream);
    for (var key in top) {
      var val = top[key];
      this[key] = val;
    }
    if (this.version < 2) {
      if (this.topDictIndex.length !== 1) {
        throw new Error("Only a single font is allowed in CFF");
      }
      this.topDict = this.topDictIndex[0];
    }
    this.isCIDFont = this.topDict.ROS != null;
    return this;
  };
  _proto9.string = function string(sid) {
    if (this.version >= 2) {
      return null;
    }
    if (sid < standardStrings.length) {
      return standardStrings[sid];
    }
    return this.stringIndex[sid - standardStrings.length];
  };
  _proto9.getCharString = function getCharString(glyph) {
    this.stream.pos = this.topDict.CharStrings[glyph].offset;
    return this.stream.readBuffer(this.topDict.CharStrings[glyph].length);
  };
  _proto9.getGlyphName = function getGlyphName(gid) {
    // CFF2 glyph names are in the post table.
    if (this.version >= 2) {
      return null;
    } // CID-keyed fonts don't have glyph names

    if (this.isCIDFont) {
      return null;
    }
    var charset = this.topDict.charset;
    if (Array.isArray(charset)) {
      return charset[gid];
    }
    if (gid === 0) {
      return '.notdef';
    }
    gid -= 1;
    switch (charset.version) {
      case 0:
        return this.string(charset.glyphs[gid]);
      case 1:
      case 2:
        for (var i = 0; i < charset.ranges.length; i++) {
          var range = charset.ranges[i];
          if (range.offset <= gid && gid <= range.offset + range.nLeft) {
            return this.string(range.first + (gid - range.offset));
          }
        }
        break;
    }
    return null;
  };
  _proto9.fdForGlyph = function fdForGlyph(gid) {
    if (!this.topDict.FDSelect) {
      return null;
    }
    switch (this.topDict.FDSelect.version) {
      case 0:
        return this.topDict.FDSelect.fds[gid];
      case 3:
      case 4:
        var ranges = this.topDict.FDSelect.ranges;
        var low = 0;
        var high = ranges.length - 1;
        while (low <= high) {
          var mid = low + high >> 1;
          if (gid < ranges[mid].first) {
            high = mid - 1;
          } else if (mid < high && gid >= ranges[mid + 1].first) {
            low = mid + 1;
          } else {
            return ranges[mid].fd;
          }
        }
      default:
        throw new Error("Unknown FDSelect version: ".concat(this.topDict.FDSelect.version));
    }
  };
  _proto9.privateDictForGlyph = function privateDictForGlyph(gid) {
    if (this.topDict.FDSelect) {
      var fd = this.fdForGlyph(gid);
      if (this.topDict.FDArray[fd]) {
        return this.topDict.FDArray[fd].Private;
      }
      return null;
    }
    if (this.version < 2) {
      return this.topDict.Private;
    }
    return this.topDict.FDArray[0].Private;
  };
  return _createClass(CFFFont, [{
    key: "postscriptName",
    get: function get() {
      if (this.version < 2) {
        return this.nameIndex[0];
      }
      return null;
    }
  }, {
    key: "fullName",
    get: function get() {
      return this.string(this.topDict.FullName);
    }
  }, {
    key: "familyName",
    get: function get() {
      return this.string(this.topDict.FamilyName);
    }
  }]);
}();
var VerticalOrigin = new r.Struct({
  glyphIndex: r.uint16,
  vertOriginY: r.int16
});
var VORG = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  defaultVertOriginY: r.int16,
  numVertOriginYMetrics: r.uint16,
  metrics: new r.Array(VerticalOrigin, 'numVertOriginYMetrics')
});
var BigMetrics = new r.Struct({
  height: r.uint8,
  width: r.uint8,
  horiBearingX: r.int8,
  horiBearingY: r.int8,
  horiAdvance: r.uint8,
  vertBearingX: r.int8,
  vertBearingY: r.int8,
  vertAdvance: r.uint8
});
var SmallMetrics = new r.Struct({
  height: r.uint8,
  width: r.uint8,
  bearingX: r.int8,
  bearingY: r.int8,
  advance: r.uint8
});
var EBDTComponent = new r.Struct({
  glyph: r.uint16,
  xOffset: r.int8,
  yOffset: r.int8
});
var ByteAligned = function ByteAligned() {};
var BitAligned = function BitAligned() {};
var glyph = new r.VersionedStruct('version', {
  1: {
    metrics: SmallMetrics,
    data: ByteAligned
  },
  2: {
    metrics: SmallMetrics,
    data: BitAligned
  },
  // format 3 is deprecated
  // format 4 is not supported by Microsoft
  5: {
    data: BitAligned
  },
  6: {
    metrics: BigMetrics,
    data: ByteAligned
  },
  7: {
    metrics: BigMetrics,
    data: BitAligned
  },
  8: {
    metrics: SmallMetrics,
    pad: new r.Reserved(r.uint8),
    numComponents: r.uint16,
    components: new r.Array(EBDTComponent, 'numComponents')
  },
  9: {
    metrics: BigMetrics,
    pad: new r.Reserved(r.uint8),
    numComponents: r.uint16,
    components: new r.Array(EBDTComponent, 'numComponents')
  },
  17: {
    metrics: SmallMetrics,
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  },
  18: {
    metrics: BigMetrics,
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  },
  19: {
    dataLen: r.uint32,
    data: new r.Buffer('dataLen')
  }
});
var SBitLineMetrics = new r.Struct({
  ascender: r.int8,
  descender: r.int8,
  widthMax: r.uint8,
  caretSlopeNumerator: r.int8,
  caretSlopeDenominator: r.int8,
  caretOffset: r.int8,
  minOriginSB: r.int8,
  minAdvanceSB: r.int8,
  maxBeforeBL: r.int8,
  minAfterBL: r.int8,
  pad: new r.Reserved(r.int8, 2)
});
var CodeOffsetPair = new r.Struct({
  glyphCode: r.uint16,
  offset: r.uint16
});
var IndexSubtable = new r.VersionedStruct(r.uint16, {
  header: {
    imageFormat: r.uint16,
    imageDataOffset: r.uint32
  },
  1: {
    offsetArray: new r.Array(r.uint32, function (t) {
      return t.parent.lastGlyphIndex - t.parent.firstGlyphIndex + 1;
    })
  },
  2: {
    imageSize: r.uint32,
    bigMetrics: BigMetrics
  },
  3: {
    offsetArray: new r.Array(r.uint16, function (t) {
      return t.parent.lastGlyphIndex - t.parent.firstGlyphIndex + 1;
    })
  },
  4: {
    numGlyphs: r.uint32,
    glyphArray: new r.Array(CodeOffsetPair, function (t) {
      return t.numGlyphs + 1;
    })
  },
  5: {
    imageSize: r.uint32,
    bigMetrics: BigMetrics,
    numGlyphs: r.uint32,
    glyphCodeArray: new r.Array(r.uint16, 'numGlyphs')
  }
});
var IndexSubtableArray = new r.Struct({
  firstGlyphIndex: r.uint16,
  lastGlyphIndex: r.uint16,
  subtable: new r.Pointer(r.uint32, IndexSubtable)
});
var BitmapSizeTable = new r.Struct({
  indexSubTableArray: new r.Pointer(r.uint32, new r.Array(IndexSubtableArray, 1), {
    type: 'parent'
  }),
  indexTablesSize: r.uint32,
  numberOfIndexSubTables: r.uint32,
  colorRef: r.uint32,
  hori: SBitLineMetrics,
  vert: SBitLineMetrics,
  startGlyphIndex: r.uint16,
  endGlyphIndex: r.uint16,
  ppemX: r.uint8,
  ppemY: r.uint8,
  bitDepth: r.uint8,
  flags: new r.Bitfield(r.uint8, ['horizontal', 'vertical'])
});
var EBLC = new r.Struct({
  version: r.uint32,
  // 0x00020000
  numSizes: r.uint32,
  sizes: new r.Array(BitmapSizeTable, 'numSizes')
});
var ImageTable = new r.Struct({
  ppem: r.uint16,
  resolution: r.uint16,
  imageOffsets: new r.Array(new r.Pointer(r.uint32, 'void'), function (t) {
    return t.parent.parent.maxp.numGlyphs + 1;
  })
}); // This is the Apple sbix table, used by the "Apple Color Emoji" font.
// It includes several image tables with images for each bitmap glyph
// of several different sizes.

var sbix = new r.Struct({
  version: r.uint16,
  flags: new r.Bitfield(r.uint16, ['renderOutlines']),
  numImgTables: r.uint32,
  imageTables: new r.Array(new r.Pointer(r.uint32, ImageTable), 'numImgTables')
});
var LayerRecord = new r.Struct({
  gid: r.uint16,
  // Glyph ID of layer glyph (must be in z-order from bottom to top).
  paletteIndex: r.uint16 // Index value to use in the appropriate palette. This value must
}); // be less than numPaletteEntries in the CPAL table, except for
// the special case noted below. Each palette entry is 16 bits.
// A palette index of 0xFFFF is a special case indicating that
// the text foreground color should be used.

var BaseGlyphRecord = new r.Struct({
  gid: r.uint16,
  // Glyph ID of reference glyph. This glyph is for reference only
  // and is not rendered for color.
  firstLayerIndex: r.uint16,
  // Index (from beginning of the Layer Records) to the layer record.
  // There will be numLayers consecutive entries for this base glyph.
  numLayers: r.uint16
});
var COLR = new r.Struct({
  version: r.uint16,
  numBaseGlyphRecords: r.uint16,
  baseGlyphRecord: new r.Pointer(r.uint32, new r.Array(BaseGlyphRecord, 'numBaseGlyphRecords')),
  layerRecords: new r.Pointer(r.uint32, new r.Array(LayerRecord, 'numLayerRecords'), {
    lazy: true
  }),
  numLayerRecords: r.uint16
});
var ColorRecord = new r.Struct({
  blue: r.uint8,
  green: r.uint8,
  red: r.uint8,
  alpha: r.uint8
});
var CPAL = new r.VersionedStruct(r.uint16, {
  header: {
    numPaletteEntries: r.uint16,
    numPalettes: r.uint16,
    numColorRecords: r.uint16,
    colorRecords: new r.Pointer(r.uint32, new r.Array(ColorRecord, 'numColorRecords')),
    colorRecordIndices: new r.Array(r.uint16, 'numPalettes')
  },
  0: {},
  1: {
    offsetPaletteTypeArray: new r.Pointer(r.uint32, new r.Array(r.uint32, 'numPalettes')),
    offsetPaletteLabelArray: new r.Pointer(r.uint32, new r.Array(r.uint16, 'numPalettes')),
    offsetPaletteEntryLabelArray: new r.Pointer(r.uint32, new r.Array(r.uint16, 'numPaletteEntries'))
  }
});
var BaseCoord = new r.VersionedStruct(r.uint16, {
  1: {
    // Design units only
    coordinate: r.int16 // X or Y value, in design units
  },
  2: {
    // Design units plus contour point
    coordinate: r.int16,
    // X or Y value, in design units
    referenceGlyph: r.uint16,
    // GlyphID of control glyph
    baseCoordPoint: r.uint16 // Index of contour point on the referenceGlyph
  },
  3: {
    // Design units plus Device table
    coordinate: r.int16,
    // X or Y value, in design units
    deviceTable: new r.Pointer(r.uint16, Device) // Device table for X or Y value
  }
});
var BaseValues = new r.Struct({
  defaultIndex: r.uint16,
  // Index of default baseline for this script-same index in the BaseTagList
  baseCoordCount: r.uint16,
  baseCoords: new r.Array(new r.Pointer(r.uint16, BaseCoord), 'baseCoordCount')
});
var FeatMinMaxRecord = new r.Struct({
  tag: new r.String(4),
  // 4-byte feature identification tag-must match FeatureTag in FeatureList
  minCoord: new r.Pointer(r.uint16, BaseCoord, {
    type: 'parent'
  }),
  // May be NULL
  maxCoord: new r.Pointer(r.uint16, BaseCoord, {
    type: 'parent'
  }) // May be NULL
});
var MinMax = new r.Struct({
  minCoord: new r.Pointer(r.uint16, BaseCoord),
  // May be NULL
  maxCoord: new r.Pointer(r.uint16, BaseCoord),
  // May be NULL
  featMinMaxCount: r.uint16,
  // May be 0
  featMinMaxRecords: new r.Array(FeatMinMaxRecord, 'featMinMaxCount') // In alphabetical order
});
var BaseLangSysRecord = new r.Struct({
  tag: new r.String(4),
  // 4-byte language system identification tag
  minMax: new r.Pointer(r.uint16, MinMax, {
    type: 'parent'
  })
});
var BaseScript = new r.Struct({
  baseValues: new r.Pointer(r.uint16, BaseValues),
  // May be NULL
  defaultMinMax: new r.Pointer(r.uint16, MinMax),
  // May be NULL
  baseLangSysCount: r.uint16,
  // May be 0
  baseLangSysRecords: new r.Array(BaseLangSysRecord, 'baseLangSysCount') // in alphabetical order by BaseLangSysTag
});
var BaseScriptRecord = new r.Struct({
  tag: new r.String(4),
  // 4-byte script identification tag
  script: new r.Pointer(r.uint16, BaseScript, {
    type: 'parent'
  })
});
var BaseScriptList = new r.Array(BaseScriptRecord, r.uint16); // Array of 4-byte baseline identification tags-must be in alphabetical order

var BaseTagList = new r.Array(new r.String(4), r.uint16);
var Axis = new r.Struct({
  baseTagList: new r.Pointer(r.uint16, BaseTagList),
  // May be NULL
  baseScriptList: new r.Pointer(r.uint16, BaseScriptList)
});
var BASE = new r.VersionedStruct(r.uint32, {
  header: {
    horizAxis: new r.Pointer(r.uint16, Axis),
    // May be NULL
    vertAxis: new r.Pointer(r.uint16, Axis) // May be NULL
  },
  0x00010000: {},
  0x00010001: {
    itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore)
  }
});
var AttachPoint = new r.Array(r.uint16, r.uint16);
var AttachList = new r.Struct({
  coverage: new r.Pointer(r.uint16, Coverage),
  glyphCount: r.uint16,
  attachPoints: new r.Array(new r.Pointer(r.uint16, AttachPoint), 'glyphCount')
});
var CaretValue = new r.VersionedStruct(r.uint16, {
  1: {
    // Design units only
    coordinate: r.int16
  },
  2: {
    // Contour point
    caretValuePoint: r.uint16
  },
  3: {
    // Design units plus Device table
    coordinate: r.int16,
    deviceTable: new r.Pointer(r.uint16, Device)
  }
});
var LigGlyph = new r.Array(new r.Pointer(r.uint16, CaretValue), r.uint16);
var LigCaretList = new r.Struct({
  coverage: new r.Pointer(r.uint16, Coverage),
  ligGlyphCount: r.uint16,
  ligGlyphs: new r.Array(new r.Pointer(r.uint16, LigGlyph), 'ligGlyphCount')
});
var MarkGlyphSetsDef = new r.Struct({
  markSetTableFormat: r.uint16,
  markSetCount: r.uint16,
  coverage: new r.Array(new r.Pointer(r.uint32, Coverage), 'markSetCount')
});
var GDEF = new r.VersionedStruct(r.uint32, {
  header: {
    glyphClassDef: new r.Pointer(r.uint16, ClassDef),
    attachList: new r.Pointer(r.uint16, AttachList),
    ligCaretList: new r.Pointer(r.uint16, LigCaretList),
    markAttachClassDef: new r.Pointer(r.uint16, ClassDef)
  },
  0x00010000: {},
  0x00010002: {
    markGlyphSetsDef: new r.Pointer(r.uint16, MarkGlyphSetsDef)
  },
  0x00010003: {
    markGlyphSetsDef: new r.Pointer(r.uint16, MarkGlyphSetsDef),
    itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore)
  }
});
var ValueFormat = new r.Bitfield(r.uint16, ['xPlacement', 'yPlacement', 'xAdvance', 'yAdvance', 'xPlaDevice', 'yPlaDevice', 'xAdvDevice', 'yAdvDevice']);
var types = {
  xPlacement: r.int16,
  yPlacement: r.int16,
  xAdvance: r.int16,
  yAdvance: r.int16,
  xPlaDevice: new r.Pointer(r.uint16, Device, {
    type: 'global',
    relativeTo: function relativeTo(ctx) {
      return ctx.rel;
    }
  }),
  yPlaDevice: new r.Pointer(r.uint16, Device, {
    type: 'global',
    relativeTo: function relativeTo(ctx) {
      return ctx.rel;
    }
  }),
  xAdvDevice: new r.Pointer(r.uint16, Device, {
    type: 'global',
    relativeTo: function relativeTo(ctx) {
      return ctx.rel;
    }
  }),
  yAdvDevice: new r.Pointer(r.uint16, Device, {
    type: 'global',
    relativeTo: function relativeTo(ctx) {
      return ctx.rel;
    }
  })
};
var ValueRecord = /*#__PURE__*/function () {
  function ValueRecord(key) {
    if (key === void 0) {
      key = 'valueFormat';
    }
    this.key = key;
  }
  var _proto10 = ValueRecord.prototype;
  _proto10.buildStruct = function buildStruct(parent) {
    var struct = parent;
    while (!struct[this.key] && struct.parent) {
      struct = struct.parent;
    }
    if (!struct[this.key]) return;
    var fields = {};
    fields.rel = function () {
      return struct._startOffset;
    };
    var format = struct[this.key];
    for (var key in format) {
      if (format[key]) {
        fields[key] = types[key];
      }
    }
    return new r.Struct(fields);
  };
  _proto10.size = function size(val, ctx) {
    return this.buildStruct(ctx).size(val, ctx);
  };
  _proto10.decode = function decode(stream, parent) {
    var res = this.buildStruct(parent).decode(stream, parent);
    delete res.rel;
    return res;
  };
  return ValueRecord;
}();
var PairValueRecord = new r.Struct({
  secondGlyph: r.uint16,
  value1: new ValueRecord('valueFormat1'),
  value2: new ValueRecord('valueFormat2')
});
var PairSet = new r.Array(PairValueRecord, r.uint16);
var Class2Record = new r.Struct({
  value1: new ValueRecord('valueFormat1'),
  value2: new ValueRecord('valueFormat2')
});
var Anchor = new r.VersionedStruct(r.uint16, {
  1: {
    // Design units only
    xCoordinate: r.int16,
    yCoordinate: r.int16
  },
  2: {
    // Design units plus contour point
    xCoordinate: r.int16,
    yCoordinate: r.int16,
    anchorPoint: r.uint16
  },
  3: {
    // Design units plus Device tables
    xCoordinate: r.int16,
    yCoordinate: r.int16,
    xDeviceTable: new r.Pointer(r.uint16, Device),
    yDeviceTable: new r.Pointer(r.uint16, Device)
  }
});
var EntryExitRecord = new r.Struct({
  entryAnchor: new r.Pointer(r.uint16, Anchor, {
    type: 'parent'
  }),
  exitAnchor: new r.Pointer(r.uint16, Anchor, {
    type: 'parent'
  })
});
var MarkRecord = new r.Struct({
  class: r.uint16,
  markAnchor: new r.Pointer(r.uint16, Anchor, {
    type: 'parent'
  })
});
var MarkArray = new r.Array(MarkRecord, r.uint16);
var BaseRecord = new r.Array(new r.Pointer(r.uint16, Anchor), function (t) {
  return t.parent.classCount;
});
var BaseArray = new r.Array(BaseRecord, r.uint16);
var ComponentRecord = new r.Array(new r.Pointer(r.uint16, Anchor), function (t) {
  return t.parent.parent.classCount;
});
var LigatureAttach = new r.Array(ComponentRecord, r.uint16);
var LigatureArray = new r.Array(new r.Pointer(r.uint16, LigatureAttach), r.uint16);
var GPOSLookup = new r.VersionedStruct('lookupType', {
  1: new r.VersionedStruct(r.uint16, {
    // Single Adjustment
    1: {
      // Single positioning value
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat: ValueFormat,
      value: new ValueRecord()
    },
    2: {
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat: ValueFormat,
      valueCount: r.uint16,
      values: new r.LazyArray(new ValueRecord(), 'valueCount')
    }
  }),
  2: new r.VersionedStruct(r.uint16, {
    // Pair Adjustment Positioning
    1: {
      // Adjustments for glyph pairs
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat1: ValueFormat,
      valueFormat2: ValueFormat,
      pairSetCount: r.uint16,
      pairSets: new r.LazyArray(new r.Pointer(r.uint16, PairSet), 'pairSetCount')
    },
    2: {
      // Class pair adjustment
      coverage: new r.Pointer(r.uint16, Coverage),
      valueFormat1: ValueFormat,
      valueFormat2: ValueFormat,
      classDef1: new r.Pointer(r.uint16, ClassDef),
      classDef2: new r.Pointer(r.uint16, ClassDef),
      class1Count: r.uint16,
      class2Count: r.uint16,
      classRecords: new r.LazyArray(new r.LazyArray(Class2Record, 'class2Count'), 'class1Count')
    }
  }),
  3: {
    // Cursive Attachment Positioning
    format: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    entryExitCount: r.uint16,
    entryExitRecords: new r.Array(EntryExitRecord, 'entryExitCount')
  },
  4: {
    // MarkToBase Attachment Positioning
    format: r.uint16,
    markCoverage: new r.Pointer(r.uint16, Coverage),
    baseCoverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    markArray: new r.Pointer(r.uint16, MarkArray),
    baseArray: new r.Pointer(r.uint16, BaseArray)
  },
  5: {
    // MarkToLigature Attachment Positioning
    format: r.uint16,
    markCoverage: new r.Pointer(r.uint16, Coverage),
    ligatureCoverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    markArray: new r.Pointer(r.uint16, MarkArray),
    ligatureArray: new r.Pointer(r.uint16, LigatureArray)
  },
  6: {
    // MarkToMark Attachment Positioning
    format: r.uint16,
    mark1Coverage: new r.Pointer(r.uint16, Coverage),
    mark2Coverage: new r.Pointer(r.uint16, Coverage),
    classCount: r.uint16,
    mark1Array: new r.Pointer(r.uint16, MarkArray),
    mark2Array: new r.Pointer(r.uint16, BaseArray)
  },
  7: Context,
  // Contextual positioning
  8: ChainingContext,
  // Chaining contextual positioning
  9: {
    // Extension Positioning
    posFormat: r.uint16,
    lookupType: r.uint16,
    // cannot also be 9
    extension: new r.Pointer(r.uint32, GPOSLookup)
  }
}); // Fix circular reference

GPOSLookup.versions[9].extension.type = GPOSLookup;
var GPOS = new r.VersionedStruct(r.uint32, {
  header: {
    scriptList: new r.Pointer(r.uint16, ScriptList),
    featureList: new r.Pointer(r.uint16, FeatureList),
    lookupList: new r.Pointer(r.uint16, new LookupList(GPOSLookup))
  },
  0x00010000: {},
  0x00010001: {
    featureVariations: new r.Pointer(r.uint32, FeatureVariations)
  }
}); // export GPOSLookup for JSTF table

var Sequence = new r.Array(r.uint16, r.uint16);
var AlternateSet = Sequence;
var Ligature = new r.Struct({
  glyph: r.uint16,
  compCount: r.uint16,
  components: new r.Array(r.uint16, function (t) {
    return t.compCount - 1;
  })
});
var LigatureSet = new r.Array(new r.Pointer(r.uint16, Ligature), r.uint16);
var GSUBLookup = new r.VersionedStruct('lookupType', {
  1: new r.VersionedStruct(r.uint16, {
    // Single Substitution
    1: {
      coverage: new r.Pointer(r.uint16, Coverage),
      deltaGlyphID: r.int16
    },
    2: {
      coverage: new r.Pointer(r.uint16, Coverage),
      glyphCount: r.uint16,
      substitute: new r.LazyArray(r.uint16, 'glyphCount')
    }
  }),
  2: {
    // Multiple Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    sequences: new r.LazyArray(new r.Pointer(r.uint16, Sequence), 'count')
  },
  3: {
    // Alternate Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    alternateSet: new r.LazyArray(new r.Pointer(r.uint16, AlternateSet), 'count')
  },
  4: {
    // Ligature Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    count: r.uint16,
    ligatureSets: new r.LazyArray(new r.Pointer(r.uint16, LigatureSet), 'count')
  },
  5: Context,
  // Contextual Substitution
  6: ChainingContext,
  // Chaining Contextual Substitution
  7: {
    // Extension Substitution
    substFormat: r.uint16,
    lookupType: r.uint16,
    // cannot also be 7
    extension: new r.Pointer(r.uint32, GSUBLookup)
  },
  8: {
    // Reverse Chaining Contextual Single Substitution
    substFormat: r.uint16,
    coverage: new r.Pointer(r.uint16, Coverage),
    backtrackCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'backtrackGlyphCount'),
    lookaheadGlyphCount: r.uint16,
    lookaheadCoverage: new r.Array(new r.Pointer(r.uint16, Coverage), 'lookaheadGlyphCount'),
    glyphCount: r.uint16,
    substitutes: new r.Array(r.uint16, 'glyphCount')
  }
}); // Fix circular reference

GSUBLookup.versions[7].extension.type = GSUBLookup;
var GSUB = new r.VersionedStruct(r.uint32, {
  header: {
    scriptList: new r.Pointer(r.uint16, ScriptList),
    featureList: new r.Pointer(r.uint16, FeatureList),
    lookupList: new r.Pointer(r.uint16, new LookupList(GSUBLookup))
  },
  0x00010000: {},
  0x00010001: {
    featureVariations: new r.Pointer(r.uint32, FeatureVariations)
  }
});
var JstfGSUBModList = new r.Array(r.uint16, r.uint16);
var JstfPriority = new r.Struct({
  shrinkageEnableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageDisableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageEnableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageDisableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  shrinkageJstfMax: new r.Pointer(r.uint16, new LookupList(GPOSLookup)),
  extensionEnableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionDisableGSUB: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionEnableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionDisableGPOS: new r.Pointer(r.uint16, JstfGSUBModList),
  extensionJstfMax: new r.Pointer(r.uint16, new LookupList(GPOSLookup))
});
var JstfLangSys = new r.Array(new r.Pointer(r.uint16, JstfPriority), r.uint16);
var JstfLangSysRecord = new r.Struct({
  tag: new r.String(4),
  jstfLangSys: new r.Pointer(r.uint16, JstfLangSys)
});
var JstfScript = new r.Struct({
  extenderGlyphs: new r.Pointer(r.uint16, new r.Array(r.uint16, r.uint16)),
  // array of glyphs to extend line length
  defaultLangSys: new r.Pointer(r.uint16, JstfLangSys),
  langSysCount: r.uint16,
  langSysRecords: new r.Array(JstfLangSysRecord, 'langSysCount')
});
var JstfScriptRecord = new r.Struct({
  tag: new r.String(4),
  script: new r.Pointer(r.uint16, JstfScript, {
    type: 'parent'
  })
});
var JSTF = new r.Struct({
  version: r.uint32,
  // should be 0x00010000
  scriptCount: r.uint16,
  scriptList: new r.Array(JstfScriptRecord, 'scriptCount')
});
var VariableSizeNumber = /*#__PURE__*/function () {
  function VariableSizeNumber(size) {
    this._size = size;
  }
  var _proto11 = VariableSizeNumber.prototype;
  _proto11.decode = function decode(stream, parent) {
    switch (this.size(0, parent)) {
      case 1:
        return stream.readUInt8();
      case 2:
        return stream.readUInt16BE();
      case 3:
        return stream.readUInt24BE();
      case 4:
        return stream.readUInt32BE();
    }
  };
  _proto11.size = function size(val, parent) {
    return utils.resolveLength(this._size, null, parent);
  };
  return VariableSizeNumber;
}();
var MapDataEntry = new r.Struct({
  entry: new VariableSizeNumber(function (t) {
    return ((t.parent.entryFormat & 0x0030) >> 4) + 1;
  }),
  outerIndex: function outerIndex(t) {
    return t.entry >> (t.parent.entryFormat & 0x000F) + 1;
  },
  innerIndex: function innerIndex(t) {
    return t.entry & (1 << (t.parent.entryFormat & 0x000F) + 1) - 1;
  }
});
var DeltaSetIndexMap = new r.Struct({
  entryFormat: r.uint16,
  mapCount: r.uint16,
  mapData: new r.Array(MapDataEntry, 'mapCount')
});
var HVAR = new r.Struct({
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  itemVariationStore: new r.Pointer(r.uint32, ItemVariationStore),
  advanceWidthMapping: new r.Pointer(r.uint32, DeltaSetIndexMap),
  LSBMapping: new r.Pointer(r.uint32, DeltaSetIndexMap),
  RSBMapping: new r.Pointer(r.uint32, DeltaSetIndexMap)
});
var Signature = new r.Struct({
  format: r.uint32,
  length: r.uint32,
  offset: r.uint32
});
var SignatureBlock = new r.Struct({
  reserved: new r.Reserved(r.uint16, 2),
  cbSignature: r.uint32,
  // Length (in bytes) of the PKCS#7 packet in pbSignature
  signature: new r.Buffer('cbSignature')
});
var DSIG = new r.Struct({
  ulVersion: r.uint32,
  // Version number of the DSIG table (0x00000001)
  usNumSigs: r.uint16,
  // Number of signatures in the table
  usFlag: r.uint16,
  // Permission flags
  signatures: new r.Array(Signature, 'usNumSigs'),
  signatureBlocks: new r.Array(SignatureBlock, 'usNumSigs')
});
var GaspRange = new r.Struct({
  rangeMaxPPEM: r.uint16,
  // Upper limit of range, in ppem
  rangeGaspBehavior: new r.Bitfield(r.uint16, [
  // Flags describing desired rasterizer behavior
  'grayscale', 'gridfit', 'symmetricSmoothing', 'symmetricGridfit' // only in version 1, for ClearType
  ])
});
var gasp = new r.Struct({
  version: r.uint16,
  // set to 0
  numRanges: r.uint16,
  gaspRanges: new r.Array(GaspRange, 'numRanges') // Sorted by ppem
});
var DeviceRecord = new r.Struct({
  pixelSize: r.uint8,
  maximumWidth: r.uint8,
  widths: new r.Array(r.uint8, function (t) {
    return t.parent.parent.maxp.numGlyphs;
  })
}); // The Horizontal Device Metrics table stores integer advance widths scaled to particular pixel sizes

var hdmx = new r.Struct({
  version: r.uint16,
  numRecords: r.int16,
  sizeDeviceRecord: r.int32,
  records: new r.Array(DeviceRecord, 'numRecords')
});
var KernPair = new r.Struct({
  left: r.uint16,
  right: r.uint16,
  value: r.int16
});
var ClassTable = new r.Struct({
  firstGlyph: r.uint16,
  nGlyphs: r.uint16,
  offsets: new r.Array(r.uint16, 'nGlyphs'),
  max: function max(t) {
    return t.offsets.length && Math.max.apply(Math, t.offsets);
  }
});
var Kern2Array = new r.Struct({
  off: function off(t) {
    return t._startOffset - t.parent.parent._startOffset;
  },
  len: function len(t) {
    return ((t.parent.leftTable.max - t.off) / t.parent.rowWidth + 1) * (t.parent.rowWidth / 2);
  },
  values: new r.LazyArray(r.int16, 'len')
});
var KernSubtable = new r.VersionedStruct('format', {
  0: {
    nPairs: r.uint16,
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16,
    pairs: new r.Array(KernPair, 'nPairs')
  },
  2: {
    rowWidth: r.uint16,
    leftTable: new r.Pointer(r.uint16, ClassTable, {
      type: 'parent'
    }),
    rightTable: new r.Pointer(r.uint16, ClassTable, {
      type: 'parent'
    }),
    array: new r.Pointer(r.uint16, Kern2Array, {
      type: 'parent'
    })
  },
  3: {
    glyphCount: r.uint16,
    kernValueCount: r.uint8,
    leftClassCount: r.uint8,
    rightClassCount: r.uint8,
    flags: r.uint8,
    kernValue: new r.Array(r.int16, 'kernValueCount'),
    leftClass: new r.Array(r.uint8, 'glyphCount'),
    rightClass: new r.Array(r.uint8, 'glyphCount'),
    kernIndex: new r.Array(r.uint8, function (t) {
      return t.leftClassCount * t.rightClassCount;
    })
  }
});
var KernTable = new r.VersionedStruct('version', {
  0: {
    // Microsoft uses this format
    subVersion: r.uint16,
    // Microsoft has an extra sub-table version number
    length: r.uint16,
    // Length of the subtable, in bytes
    format: r.uint8,
    // Format of subtable
    coverage: new r.Bitfield(r.uint8, ['horizontal',
    // 1 if table has horizontal data, 0 if vertical
    'minimum',
    // If set to 1, the table has minimum values. If set to 0, the table has kerning values.
    'crossStream',
    // If set to 1, kerning is perpendicular to the flow of the text
    'override' // If set to 1 the value in this table replaces the accumulated value
    ]),
    subtable: KernSubtable,
    padding: new r.Reserved(r.uint8, function (t) {
      return t.length - t._currentOffset;
    })
  },
  1: {
    // Apple uses this format
    length: r.uint32,
    coverage: new r.Bitfield(r.uint8, [null, null, null, null, null, 'variation',
    // Set if table has variation kerning values
    'crossStream',
    // Set if table has cross-stream kerning values
    'vertical' // Set if table has vertical kerning values
    ]),
    format: r.uint8,
    tupleIndex: r.uint16,
    subtable: KernSubtable,
    padding: new r.Reserved(r.uint8, function (t) {
      return t.length - t._currentOffset;
    })
  }
});
var kern = new r.VersionedStruct(r.uint16, {
  0: {
    // Microsoft Version
    nTables: r.uint16,
    tables: new r.Array(KernTable, 'nTables')
  },
  1: {
    // Apple Version
    reserved: new r.Reserved(r.uint16),
    // the other half of the version number
    nTables: r.uint32,
    tables: new r.Array(KernTable, 'nTables')
  }
});

// Records the ppem for each glyph at which the scaling becomes linear again,
// despite instructions effecting the advance width

var LTSH = new r.Struct({
  version: r.uint16,
  numGlyphs: r.uint16,
  yPels: new r.Array(r.uint8, 'numGlyphs')
});

// NOTE: The PCLT table is strongly discouraged for OpenType fonts with TrueType outlines

var PCLT = new r.Struct({
  version: r.uint16,
  fontNumber: r.uint32,
  pitch: r.uint16,
  xHeight: r.uint16,
  style: r.uint16,
  typeFamily: r.uint16,
  capHeight: r.uint16,
  symbolSet: r.uint16,
  typeface: new r.String(16),
  characterComplement: new r.String(8),
  fileName: new r.String(6),
  strokeWeight: new r.String(1),
  widthType: new r.String(1),
  serifStyle: r.uint8,
  reserved: new r.Reserved(r.uint8)
});

// sizes. This is needed in order to match font metrics on Windows.

var Ratio = new r.Struct({
  bCharSet: r.uint8,
  // Character set
  xRatio: r.uint8,
  // Value to use for x-Ratio
  yStartRatio: r.uint8,
  // Starting y-Ratio value
  yEndRatio: r.uint8 // Ending y-Ratio value
});
var vTable = new r.Struct({
  yPelHeight: r.uint16,
  // yPelHeight to which values apply
  yMax: r.int16,
  // Maximum value (in pels) for this yPelHeight
  yMin: r.int16 // Minimum value (in pels) for this yPelHeight
});
var VdmxGroup = new r.Struct({
  recs: r.uint16,
  // Number of height records in this group
  startsz: r.uint8,
  // Starting yPelHeight
  endsz: r.uint8,
  // Ending yPelHeight
  entries: new r.Array(vTable, 'recs') // The VDMX records
});
var VDMX = new r.Struct({
  version: r.uint16,
  // Version number (0 or 1)
  numRecs: r.uint16,
  // Number of VDMX groups present
  numRatios: r.uint16,
  // Number of aspect ratio groupings
  ratioRanges: new r.Array(Ratio, 'numRatios'),
  // Ratio ranges
  offsets: new r.Array(r.uint16, 'numRatios'),
  // Offset to the VDMX group for this ratio range
  groups: new r.Array(VdmxGroup, 'numRecs') // The actual VDMX groupings
});
var vhea = new r.Struct({
  version: r.uint16,
  // Version number of the Vertical Header Table
  ascent: r.int16,
  // The vertical typographic ascender for this font
  descent: r.int16,
  // The vertical typographic descender for this font
  lineGap: r.int16,
  // The vertical typographic line gap for this font
  advanceHeightMax: r.int16,
  // The maximum advance height measurement found in the font
  minTopSideBearing: r.int16,
  // The minimum top side bearing measurement found in the font
  minBottomSideBearing: r.int16,
  // The minimum bottom side bearing measurement found in the font
  yMaxExtent: r.int16,
  caretSlopeRise: r.int16,
  // Caret slope (rise/run)
  caretSlopeRun: r.int16,
  caretOffset: r.int16,
  // Set value equal to 0 for nonslanted fonts
  reserved: new r.Reserved(r.int16, 4),
  metricDataFormat: r.int16,
  // Set to 0
  numberOfMetrics: r.uint16 // Number of advance heights in the Vertical Metrics table
});
var VmtxEntry = new r.Struct({
  advance: r.uint16,
  // The advance height of the glyph
  bearing: r.int16 // The top sidebearing of the glyph
}); // Vertical Metrics Table

var vmtx = new r.Struct({
  metrics: new r.LazyArray(VmtxEntry, function (t) {
    return t.parent.vhea.numberOfMetrics;
  }),
  bearings: new r.LazyArray(r.int16, function (t) {
    return t.parent.maxp.numGlyphs - t.parent.vhea.numberOfMetrics;
  })
});
var shortFrac = new r.Fixed(16, 'BE', 14);
var Correspondence = new r.Struct({
  fromCoord: shortFrac,
  toCoord: shortFrac
});
var Segment = new r.Struct({
  pairCount: r.uint16,
  correspondence: new r.Array(Correspondence, 'pairCount')
});
var avar = new r.Struct({
  version: r.fixed32,
  axisCount: r.uint32,
  segment: new r.Array(Segment, 'axisCount')
});
var UnboundedArrayAccessor = /*#__PURE__*/function () {
  function UnboundedArrayAccessor(type, stream, parent) {
    this.type = type;
    this.stream = stream;
    this.parent = parent;
    this.base = this.stream.pos;
    this._items = [];
  }
  var _proto12 = UnboundedArrayAccessor.prototype;
  _proto12.getItem = function getItem(index) {
    if (this._items[index] == null) {
      var pos = this.stream.pos;
      this.stream.pos = this.base + this.type.size(null, this.parent) * index;
      this._items[index] = this.type.decode(this.stream, this.parent);
      this.stream.pos = pos;
    }
    return this._items[index];
  };
  _proto12.inspect = function inspect() {
    return "[UnboundedArray ".concat(this.type.constructor.name, "]");
  };
  return UnboundedArrayAccessor;
}();
var UnboundedArray = /*#__PURE__*/function (_r$Array2) {
  function UnboundedArray(type) {
    return _r$Array2.call(this, type, 0) || this;
  }
  _inheritsLoose(UnboundedArray, _r$Array2);
  var _proto13 = UnboundedArray.prototype;
  _proto13.decode = function decode(stream, parent) {
    return new UnboundedArrayAccessor(this.type, stream, parent);
  };
  return UnboundedArray;
}(r.Array);
var LookupTable = function LookupTable(ValueType) {
  if (ValueType === void 0) {
    ValueType = r.uint16;
  }
  // Helper class that makes internal structures invisible to pointers
  var Shadow = /*#__PURE__*/function () {
    function Shadow(type) {
      this.type = type;
    }
    var _proto14 = Shadow.prototype;
    _proto14.decode = function decode(stream, ctx) {
      ctx = ctx.parent.parent;
      return this.type.decode(stream, ctx);
    };
    _proto14.size = function size(val, ctx) {
      ctx = ctx.parent.parent;
      return this.type.size(val, ctx);
    };
    _proto14.encode = function encode(stream, val, ctx) {
      ctx = ctx.parent.parent;
      return this.type.encode(stream, val, ctx);
    };
    return Shadow;
  }();
  ValueType = new Shadow(ValueType);
  var BinarySearchHeader = new r.Struct({
    unitSize: r.uint16,
    nUnits: r.uint16,
    searchRange: r.uint16,
    entrySelector: r.uint16,
    rangeShift: r.uint16
  });
  var LookupSegmentSingle = new r.Struct({
    lastGlyph: r.uint16,
    firstGlyph: r.uint16,
    value: ValueType
  });
  var LookupSegmentArray = new r.Struct({
    lastGlyph: r.uint16,
    firstGlyph: r.uint16,
    values: new r.Pointer(r.uint16, new r.Array(ValueType, function (t) {
      return t.lastGlyph - t.firstGlyph + 1;
    }), {
      type: 'parent'
    })
  });
  var LookupSingle = new r.Struct({
    glyph: r.uint16,
    value: ValueType
  });
  return new r.VersionedStruct(r.uint16, {
    0: {
      values: new UnboundedArray(ValueType) // length == number of glyphs maybe?
    },
    2: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSegmentSingle, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    4: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSegmentArray, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    6: {
      binarySearchHeader: BinarySearchHeader,
      segments: new r.Array(LookupSingle, function (t) {
        return t.binarySearchHeader.nUnits;
      })
    },
    8: {
      firstGlyph: r.uint16,
      count: r.uint16,
      values: new r.Array(ValueType, 'count')
    }
  });
};
function StateTable(entryData, lookupType) {
  if (entryData === void 0) {
    entryData = {};
  }
  if (lookupType === void 0) {
    lookupType = r.uint16;
  }
  var entry = Object.assign({
    newState: r.uint16,
    flags: r.uint16
  }, entryData);
  var Entry = new r.Struct(entry);
  var StateArray = new UnboundedArray(new r.Array(r.uint16, function (t) {
    return t.nClasses;
  }));
  var StateHeader = new r.Struct({
    nClasses: r.uint32,
    classTable: new r.Pointer(r.uint32, new LookupTable(lookupType)),
    stateArray: new r.Pointer(r.uint32, StateArray),
    entryTable: new r.Pointer(r.uint32, new UnboundedArray(Entry))
  });
  return StateHeader;
} // This is the old version of the StateTable structure

function StateTable1(entryData, lookupType) {
  if (entryData === void 0) {
    entryData = {};
  }
  if (lookupType === void 0) {
    lookupType = r.uint16;
  }
  var ClassLookupTable = new r.Struct({
    version: function version() {
      return 8;
    },
    // simulate LookupTable
    firstGlyph: r.uint16,
    values: new r.Array(r.uint8, r.uint16)
  });
  var entry = Object.assign({
    newStateOffset: r.uint16,
    // convert offset to stateArray index
    newState: function newState(t) {
      return (t.newStateOffset - (t.parent.stateArray.base - t.parent._startOffset)) / t.parent.nClasses;
    },
    flags: r.uint16
  }, entryData);
  var Entry = new r.Struct(entry);
  var StateArray = new UnboundedArray(new r.Array(r.uint8, function (t) {
    return t.nClasses;
  }));
  var StateHeader1 = new r.Struct({
    nClasses: r.uint16,
    classTable: new r.Pointer(r.uint16, ClassLookupTable),
    stateArray: new r.Pointer(r.uint16, StateArray),
    entryTable: new r.Pointer(r.uint16, new UnboundedArray(Entry))
  });
  return StateHeader1;
}
var BslnSubtable = new r.VersionedStruct('format', {
  0: {
    // Distance-based, no mapping
    deltas: new r.Array(r.int16, 32)
  },
  1: {
    // Distance-based, with mapping
    deltas: new r.Array(r.int16, 32),
    mappingData: new LookupTable(r.uint16)
  },
  2: {
    // Control point-based, no mapping
    standardGlyph: r.uint16,
    controlPoints: new r.Array(r.uint16, 32)
  },
  3: {
    // Control point-based, with mapping
    standardGlyph: r.uint16,
    controlPoints: new r.Array(r.uint16, 32),
    mappingData: new LookupTable(r.uint16)
  }
});
var bsln = new r.Struct({
  version: r.fixed32,
  format: r.uint16,
  defaultBaseline: r.uint16,
  subtable: BslnSubtable
});
var Setting = new r.Struct({
  setting: r.uint16,
  nameIndex: r.int16,
  name: function name(t) {
    return t.parent.parent.parent.name.records.fontFeatures[t.nameIndex];
  }
});
var FeatureName = new r.Struct({
  feature: r.uint16,
  nSettings: r.uint16,
  settingTable: new r.Pointer(r.uint32, new r.Array(Setting, 'nSettings'), {
    type: 'parent'
  }),
  featureFlags: new r.Bitfield(r.uint8, [null, null, null, null, null, null, 'hasDefault', 'exclusive']),
  defaultSetting: r.uint8,
  nameIndex: r.int16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameIndex];
  }
});
var feat = new r.Struct({
  version: r.fixed32,
  featureNameCount: r.uint16,
  reserved1: new r.Reserved(r.uint16),
  reserved2: new r.Reserved(r.uint32),
  featureNames: new r.Array(FeatureName, 'featureNameCount')
});
var Axis$1 = new r.Struct({
  axisTag: new r.String(4),
  minValue: r.fixed32,
  defaultValue: r.fixed32,
  maxValue: r.fixed32,
  flags: r.uint16,
  nameID: r.uint16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameID];
  }
});
var Instance = new r.Struct({
  nameID: r.uint16,
  name: function name(t) {
    return t.parent.parent.name.records.fontFeatures[t.nameID];
  },
  flags: r.uint16,
  coord: new r.Array(r.fixed32, function (t) {
    return t.parent.axisCount;
  }),
  postscriptNameID: new r.Optional(r.uint16, function (t) {
    return t.parent.instanceSize - t._currentOffset > 0;
  })
});
var fvar = new r.Struct({
  version: r.fixed32,
  offsetToData: r.uint16,
  countSizePairs: r.uint16,
  axisCount: r.uint16,
  axisSize: r.uint16,
  instanceCount: r.uint16,
  instanceSize: r.uint16,
  axis: new r.Array(Axis$1, 'axisCount'),
  instance: new r.Array(Instance, 'instanceCount')
});
var shortFrac$1 = new r.Fixed(16, 'BE', 14);
var Offset = /*#__PURE__*/function () {
  function Offset() {}
  Offset.decode = function decode(stream, parent) {
    // In short format, offsets are multiplied by 2.
    // This doesn't seem to be documented by Apple, but it
    // is implemented this way in Freetype.
    return parent.flags ? stream.readUInt32BE() : stream.readUInt16BE() * 2;
  };
  return Offset;
}();
var gvar = new r.Struct({
  version: r.uint16,
  reserved: new r.Reserved(r.uint16),
  axisCount: r.uint16,
  globalCoordCount: r.uint16,
  globalCoords: new r.Pointer(r.uint32, new r.Array(new r.Array(shortFrac$1, 'axisCount'), 'globalCoordCount')),
  glyphCount: r.uint16,
  flags: r.uint16,
  offsetToData: r.uint32,
  offsets: new r.Array(new r.Pointer(Offset, 'void', {
    relativeTo: function relativeTo(ctx) {
      return ctx.offsetToData;
    },
    allowNull: false
  }), function (t) {
    return t.glyphCount + 1;
  })
});
var ClassTable$1 = new r.Struct({
  length: r.uint16,
  coverage: r.uint16,
  subFeatureFlags: r.uint32,
  stateTable: new StateTable1()
});
var WidthDeltaRecord = new r.Struct({
  justClass: r.uint32,
  beforeGrowLimit: r.fixed32,
  beforeShrinkLimit: r.fixed32,
  afterGrowLimit: r.fixed32,
  afterShrinkLimit: r.fixed32,
  growFlags: r.uint16,
  shrinkFlags: r.uint16
});
var WidthDeltaCluster = new r.Array(WidthDeltaRecord, r.uint32);
var ActionData = new r.VersionedStruct('actionType', {
  0: {
    // Decomposition action
    lowerLimit: r.fixed32,
    upperLimit: r.fixed32,
    order: r.uint16,
    glyphs: new r.Array(r.uint16, r.uint16)
  },
  1: {
    // Unconditional add glyph action
    addGlyph: r.uint16
  },
  2: {
    // Conditional add glyph action
    substThreshold: r.fixed32,
    addGlyph: r.uint16,
    substGlyph: r.uint16
  },
  3: {},
  // Stretch glyph action (no data, not supported by CoreText)
  4: {
    // Ductile glyph action (not supported by CoreText)
    variationAxis: r.uint32,
    minimumLimit: r.fixed32,
    noStretchValue: r.fixed32,
    maximumLimit: r.fixed32
  },
  5: {
    // Repeated add glyph action
    flags: r.uint16,
    glyph: r.uint16
  }
});
var Action = new r.Struct({
  actionClass: r.uint16,
  actionType: r.uint16,
  actionLength: r.uint32,
  actionData: ActionData,
  padding: new r.Reserved(r.uint8, function (t) {
    return t.actionLength - t._currentOffset;
  })
});
var PostcompensationAction = new r.Array(Action, r.uint32);
var PostCompensationTable = new r.Struct({
  lookupTable: new LookupTable(new r.Pointer(r.uint16, PostcompensationAction))
});
var JustificationTable = new r.Struct({
  classTable: new r.Pointer(r.uint16, ClassTable$1, {
    type: 'parent'
  }),
  wdcOffset: r.uint16,
  postCompensationTable: new r.Pointer(r.uint16, PostCompensationTable, {
    type: 'parent'
  }),
  widthDeltaClusters: new LookupTable(new r.Pointer(r.uint16, WidthDeltaCluster, {
    type: 'parent',
    relativeTo: function relativeTo(ctx) {
      return ctx.wdcOffset;
    }
  }))
});
var just = new r.Struct({
  version: r.uint32,
  format: r.uint16,
  horizontal: new r.Pointer(r.uint16, JustificationTable),
  vertical: new r.Pointer(r.uint16, JustificationTable)
});
var LigatureData = {
  action: r.uint16
};
var ContextualData = {
  markIndex: r.uint16,
  currentIndex: r.uint16
};
var InsertionData = {
  currentInsertIndex: r.uint16,
  markedInsertIndex: r.uint16
};
var SubstitutionTable = new r.Struct({
  items: new UnboundedArray(new r.Pointer(r.uint32, new LookupTable()))
});
var SubtableData = new r.VersionedStruct('type', {
  0: {
    // Indic Rearrangement Subtable
    stateTable: new StateTable()
  },
  1: {
    // Contextual Glyph Substitution Subtable
    stateTable: new StateTable(ContextualData),
    substitutionTable: new r.Pointer(r.uint32, SubstitutionTable)
  },
  2: {
    // Ligature subtable
    stateTable: new StateTable(LigatureData),
    ligatureActions: new r.Pointer(r.uint32, new UnboundedArray(r.uint32)),
    components: new r.Pointer(r.uint32, new UnboundedArray(r.uint16)),
    ligatureList: new r.Pointer(r.uint32, new UnboundedArray(r.uint16))
  },
  4: {
    // Non-contextual Glyph Substitution Subtable
    lookupTable: new LookupTable()
  },
  5: {
    // Glyph Insertion Subtable
    stateTable: new StateTable(InsertionData),
    insertionActions: new r.Pointer(r.uint32, new UnboundedArray(r.uint16))
  }
});
var Subtable = new r.Struct({
  length: r.uint32,
  coverage: r.uint24,
  type: r.uint8,
  subFeatureFlags: r.uint32,
  table: SubtableData,
  padding: new r.Reserved(r.uint8, function (t) {
    return t.length - t._currentOffset;
  })
});
var FeatureEntry = new r.Struct({
  featureType: r.uint16,
  featureSetting: r.uint16,
  enableFlags: r.uint32,
  disableFlags: r.uint32
});
var MorxChain = new r.Struct({
  defaultFlags: r.uint32,
  chainLength: r.uint32,
  nFeatureEntries: r.uint32,
  nSubtables: r.uint32,
  features: new r.Array(FeatureEntry, 'nFeatureEntries'),
  subtables: new r.Array(Subtable, 'nSubtables')
});
var morx = new r.Struct({
  version: r.uint16,
  unused: new r.Reserved(r.uint16),
  nChains: r.uint32,
  chains: new r.Array(MorxChain, 'nChains')
});
var OpticalBounds = new r.Struct({
  left: r.int16,
  top: r.int16,
  right: r.int16,
  bottom: r.int16
});
var opbd = new r.Struct({
  version: r.fixed32,
  format: r.uint16,
  lookupTable: new LookupTable(OpticalBounds)
});
var tables = {};
tables.cmap = cmap;
tables.head = head;
tables.hhea = hhea;
tables.hmtx = hmtx;
tables.maxp = maxp;
tables.name = NameTable;
tables['OS/2'] = OS2;
tables.post = post; // TrueType Outlines
tables.fpgm = fpgm;
tables.loca = loca;
tables.prep = prep;
tables['cvt '] = cvt;
tables.glyf = glyf; // PostScript Outlines
tables['CFF '] = CFFFont;
tables['CFF2'] = CFFFont;
tables.VORG = VORG; // Bitmap Glyphs
tables.EBLC = EBLC;
tables.CBLC = tables.EBLC;
tables.sbix = sbix;
tables.COLR = COLR;
tables.CPAL = CPAL; // Advanced OpenType Tables
tables.BASE = BASE;
tables.GDEF = GDEF;
tables.GPOS = GPOS;
tables.GSUB = GSUB;
tables.JSTF = JSTF; // OpenType variations tables
tables.HVAR = HVAR; // Other OpenType Tables
tables.DSIG = DSIG;
tables.gasp = gasp;
tables.hdmx = hdmx;
tables.kern = kern;
tables.LTSH = LTSH;
tables.PCLT = PCLT;
tables.VDMX = VDMX;
tables.vhea = vhea;
tables.vmtx = vmtx; // Apple Advanced Typography Tables
tables.avar = avar;
tables.bsln = bsln;
tables.feat = feat;
tables.fvar = fvar;
tables.gvar = gvar;
tables.just = just;
tables.morx = morx;
tables.opbd = opbd;
var TableEntry = new r.Struct({
  tag: new r.String(4),
  checkSum: r.uint32,
  offset: new r.Pointer(r.uint32, 'void', {
    type: 'global'
  }),
  length: r.uint32
});
var Directory = new r.Struct({
  tag: new r.String(4),
  numTables: r.uint16,
  searchRange: r.uint16,
  entrySelector: r.uint16,
  rangeShift: r.uint16,
  tables: new r.Array(TableEntry, 'numTables')
});
Directory.process = function () {
  var tables = {};
  for (var _iterator9 = _createForOfIteratorHelperLoose(this.tables), _step9; !(_step9 = _iterator9()).done;) {
    var table = _step9.value;
    tables[table.tag] = table;
  }
  this.tables = tables;
};
Directory.preEncode = function (stream) {
  var tables$1 = [];
  for (var tag in this.tables) {
    var table = this.tables[tag];
    if (table) {
      tables$1.push({
        tag: tag,
        checkSum: 0,
        offset: new r.VoidPointer(tables[tag], table),
        length: tables[tag].size(table)
      });
    }
  }
  this.tag = 'true';
  this.numTables = tables$1.length;
  this.tables = tables$1;
  var maxExponentFor2 = Math.floor(Math.log(this.numTables) / Math.LN2);
  var maxPowerOf2 = Math.pow(2, maxExponentFor2);
  this.searchRange = maxPowerOf2 * 16;
  this.entrySelector = Math.log(maxPowerOf2) / Math.LN2;
  this.rangeShift = this.numTables * 16 - this.searchRange;
};
function binarySearch(arr, cmp) {
  var min = 0;
  var max = arr.length - 1;
  while (min <= max) {
    var mid = min + max >> 1;
    var res = cmp(arr[mid]);
    if (res < 0) {
      max = mid - 1;
    } else if (res > 0) {
      min = mid + 1;
    } else {
      return mid;
    }
  }
  return -1;
}
function range(index, end) {
  var range = [];
  while (index < end) {
    range.push(index++);
  }
  return range;
}
var _class;
try {
  var iconv = __webpack_require__(54171);
} catch (err) {}
var CmapProcessor = (_class = /*#__PURE__*/function () {
  function CmapProcessor(cmapTable) {
    // Attempt to find a Unicode cmap first
    this.encoding = null;
    this.cmap = this.findSubtable(cmapTable, [
    // 32-bit subtables
    [3, 10], [0, 6], [0, 4],
    // 16-bit subtables
    [3, 1], [0, 3], [0, 2], [0, 1], [0, 0]]); // If not unicode cmap was found, and iconv-lite is installed,
    // take the first table with a supported encoding.

    if (!this.cmap && iconv) {
      for (var _iterator10 = _createForOfIteratorHelperLoose(cmapTable.tables), _step10; !(_step10 = _iterator10()).done;) {
        var cmap = _step10.value;
        var encoding = getEncoding(cmap.platformID, cmap.encodingID, cmap.table.language - 1);
        if (iconv.encodingExists(encoding)) {
          this.cmap = cmap.table;
          this.encoding = encoding;
        }
      }
    }
    if (!this.cmap) {
      throw new Error("Could not find a supported cmap table");
    }
    this.uvs = this.findSubtable(cmapTable, [[0, 5]]);
    if (this.uvs && this.uvs.version !== 14) {
      this.uvs = null;
    }
  }
  var _proto15 = CmapProcessor.prototype;
  _proto15.findSubtable = function findSubtable(cmapTable, pairs) {
    for (var _iterator11 = _createForOfIteratorHelperLoose(pairs), _step11; !(_step11 = _iterator11()).done;) {
      var _step11$value = _step11.value,
        platformID = _step11$value[0],
        encodingID = _step11$value[1];
      for (var _iterator12 = _createForOfIteratorHelperLoose(cmapTable.tables), _step12; !(_step12 = _iterator12()).done;) {
        var cmap = _step12.value;
        if (cmap.platformID === platformID && cmap.encodingID === encodingID) {
          return cmap.table;
        }
      }
    }
    return null;
  };
  _proto15.lookup = function lookup(codepoint, variationSelector) {
    // If there is no Unicode cmap in this font, we need to re-encode
    // the codepoint in the encoding that the cmap supports.
    if (this.encoding) {
      var buf = iconv.encode(String.fromCodePoint(codepoint), this.encoding);
      codepoint = 0;
      for (var i = 0; i < buf.length; i++) {
        codepoint = codepoint << 8 | buf[i];
      } // Otherwise, try to get a Unicode variation selector for this codepoint if one is provided.
    } else if (variationSelector) {
      var gid = this.getVariationSelector(codepoint, variationSelector);
      if (gid) {
        return gid;
      }
    }
    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        return cmap.codeMap.get(codepoint) || 0;
      case 4:
        {
          var min = 0;
          var max = cmap.segCount - 1;
          while (min <= max) {
            var mid = min + max >> 1;
            if (codepoint < cmap.startCode.get(mid)) {
              max = mid - 1;
            } else if (codepoint > cmap.endCode.get(mid)) {
              min = mid + 1;
            } else {
              var rangeOffset = cmap.idRangeOffset.get(mid);
              var _gid = void 0;
              if (rangeOffset === 0) {
                _gid = codepoint + cmap.idDelta.get(mid);
              } else {
                var index = rangeOffset / 2 + (codepoint - cmap.startCode.get(mid)) - (cmap.segCount - mid);
                _gid = cmap.glyphIndexArray.get(index) || 0;
                if (_gid !== 0) {
                  _gid += cmap.idDelta.get(mid);
                }
              }
              return _gid & 0xffff;
            }
          }
          return 0;
        }
      case 8:
        throw new Error('TODO: cmap format 8');
      case 6:
      case 10:
        return cmap.glyphIndices.get(codepoint - cmap.firstCode) || 0;
      case 12:
      case 13:
        {
          var _min = 0;
          var _max = cmap.nGroups - 1;
          while (_min <= _max) {
            var _mid = _min + _max >> 1;
            var group = cmap.groups.get(_mid);
            if (codepoint < group.startCharCode) {
              _max = _mid - 1;
            } else if (codepoint > group.endCharCode) {
              _min = _mid + 1;
            } else {
              if (cmap.version === 12) {
                return group.glyphID + (codepoint - group.startCharCode);
              } else {
                return group.glyphID;
              }
            }
          }
          return 0;
        }
      case 14:
        throw new Error('TODO: cmap format 14');
      default:
        throw new Error("Unknown cmap format ".concat(cmap.version));
    }
  };
  _proto15.getVariationSelector = function getVariationSelector(codepoint, variationSelector) {
    if (!this.uvs) {
      return 0;
    }
    var selectors = this.uvs.varSelectors.toArray();
    var i = binarySearch(selectors, function (x) {
      return variationSelector - x.varSelector;
    });
    var sel = selectors[i];
    if (i !== -1 && sel.defaultUVS) {
      i = binarySearch(sel.defaultUVS, function (x) {
        return codepoint < x.startUnicodeValue ? -1 : codepoint > x.startUnicodeValue + x.additionalCount ? +1 : 0;
      });
    }
    if (i !== -1 && sel.nonDefaultUVS) {
      i = binarySearch(sel.nonDefaultUVS, function (x) {
        return codepoint - x.unicodeValue;
      });
      if (i !== -1) {
        return sel.nonDefaultUVS[i].glyphID;
      }
    }
    return 0;
  };
  _proto15.getCharacterSet = function getCharacterSet() {
    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        return range(0, cmap.codeMap.length);
      case 4:
        {
          var res = [];
          var endCodes = cmap.endCode.toArray();
          for (var i = 0; i < endCodes.length; i++) {
            var tail = endCodes[i] + 1;
            var start = cmap.startCode.get(i);
            res.push.apply(res, range(start, tail));
          }
          return res;
        }
      case 8:
        throw new Error('TODO: cmap format 8');
      case 6:
      case 10:
        return range(cmap.firstCode, cmap.firstCode + cmap.glyphIndices.length);
      case 12:
      case 13:
        {
          var _res = [];
          for (var _iterator13 = _createForOfIteratorHelperLoose(cmap.groups.toArray()), _step13; !(_step13 = _iterator13()).done;) {
            var group = _step13.value;
            _res.push.apply(_res, range(group.startCharCode, group.endCharCode + 1));
          }
          return _res;
        }
      case 14:
        throw new Error('TODO: cmap format 14');
      default:
        throw new Error("Unknown cmap format ".concat(cmap.version));
    }
  };
  _proto15.codePointsForGlyph = function codePointsForGlyph(gid) {
    var cmap = this.cmap;
    switch (cmap.version) {
      case 0:
        {
          var res = [];
          for (var i = 0; i < 256; i++) {
            if (cmap.codeMap.get(i) === gid) {
              res.push(i);
            }
          }
          return res;
        }
      case 4:
        {
          var _res2 = [];
          for (var _i = 0; _i < cmap.segCount; _i++) {
            var end = cmap.endCode.get(_i);
            var start = cmap.startCode.get(_i);
            var rangeOffset = cmap.idRangeOffset.get(_i);
            var delta = cmap.idDelta.get(_i);
            for (var c = start; c <= end; c++) {
              var g = 0;
              if (rangeOffset === 0) {
                g = c + delta;
              } else {
                var index = rangeOffset / 2 + (c - start) - (cmap.segCount - _i);
                g = cmap.glyphIndexArray.get(index) || 0;
                if (g !== 0) {
                  g += delta;
                }
              }
              if (g === gid) {
                _res2.push(c);
              }
            }
          }
          return _res2;
        }
      case 12:
        {
          var _res3 = [];
          for (var _iterator14 = _createForOfIteratorHelperLoose(cmap.groups.toArray()), _step14; !(_step14 = _iterator14()).done;) {
            var group = _step14.value;
            if (gid >= group.glyphID && gid <= group.glyphID + (group.endCharCode - group.startCharCode)) {
              _res3.push(group.startCharCode + (gid - group.glyphID));
            }
          }
          return _res3;
        }
      case 13:
        {
          var _res4 = [];
          for (var _iterator15 = _createForOfIteratorHelperLoose(cmap.groups.toArray()), _step15; !(_step15 = _iterator15()).done;) {
            var _group = _step15.value;
            if (gid === _group.glyphID) {
              _res4.push.apply(_res4, range(_group.startCharCode, _group.endCharCode + 1));
            }
          }
          return _res4;
        }
      default:
        throw new Error("Unknown cmap format ".concat(cmap.version));
    }
  };
  return CmapProcessor;
}(), _applyDecoratedDescriptor(_class.prototype, "getCharacterSet", [cache], Object.getOwnPropertyDescriptor(_class.prototype, "getCharacterSet"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "codePointsForGlyph", [cache], Object.getOwnPropertyDescriptor(_class.prototype, "codePointsForGlyph"), _class.prototype), _class);
var KernProcessor = /*#__PURE__*/function () {
  function KernProcessor(font) {
    this.kern = font.kern;
  }
  var _proto16 = KernProcessor.prototype;
  _proto16.process = function process(glyphs, positions) {
    for (var glyphIndex = 0; glyphIndex < glyphs.length - 1; glyphIndex++) {
      var left = glyphs[glyphIndex].id;
      var right = glyphs[glyphIndex + 1].id;
      positions[glyphIndex].xAdvance += this.getKerning(left, right);
    }
  };
  _proto16.getKerning = function getKerning(left, right) {
    var res = 0;
    for (var _iterator16 = _createForOfIteratorHelperLoose(this.kern.tables), _step16; !(_step16 = _iterator16()).done;) {
      var table = _step16.value;
      if (table.coverage.crossStream) {
        continue;
      }
      switch (table.version) {
        case 0:
          if (!table.coverage.horizontal) {
            continue;
          }
          break;
        case 1:
          if (table.coverage.vertical || table.coverage.variation) {
            continue;
          }
          break;
        default:
          throw new Error("Unsupported kerning table version ".concat(table.version));
      }
      var val = 0;
      var s = table.subtable;
      switch (table.format) {
        case 0:
          var pairIdx = binarySearch(s.pairs, function (pair) {
            return left - pair.left || right - pair.right;
          });
          if (pairIdx >= 0) {
            val = s.pairs[pairIdx].value;
          }
          break;
        case 2:
          var leftOffset = 0,
            rightOffset = 0;
          if (left >= s.leftTable.firstGlyph && left < s.leftTable.firstGlyph + s.leftTable.nGlyphs) {
            leftOffset = s.leftTable.offsets[left - s.leftTable.firstGlyph];
          } else {
            leftOffset = s.array.off;
          }
          if (right >= s.rightTable.firstGlyph && right < s.rightTable.firstGlyph + s.rightTable.nGlyphs) {
            rightOffset = s.rightTable.offsets[right - s.rightTable.firstGlyph];
          }
          var index = (leftOffset + rightOffset - s.array.off) / 2;
          val = s.array.values.get(index);
          break;
        case 3:
          if (left >= s.glyphCount || right >= s.glyphCount) {
            return 0;
          }
          val = s.kernValue[s.kernIndex[s.leftClass[left] * s.rightClassCount + s.rightClass[right]]];
          break;
        default:
          throw new Error("Unsupported kerning sub-table format ".concat(table.format));
      } // Microsoft supports the override flag, which resets the result
      // Otherwise, the sum of the results from all subtables is returned

      if (table.coverage.override) {
        res = val;
      } else {
        res += val;
      }
    }
    return res;
  };
  return KernProcessor;
}();
/**
 * This class is used when GPOS does not define 'mark' or 'mkmk' features
 * for positioning marks relative to base glyphs. It uses the unicode
 * combining class property to position marks.
 *
 * Based on code from Harfbuzz, thanks!
 * https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-fallback.cc
 */
var UnicodeLayoutEngine = /*#__PURE__*/function () {
  function UnicodeLayoutEngine(font) {
    this.font = font;
  }
  var _proto17 = UnicodeLayoutEngine.prototype;
  _proto17.positionGlyphs = function positionGlyphs(glyphs, positions) {
    // find each base + mark cluster, and position the marks relative to the base
    var clusterStart = 0;
    var clusterEnd = 0;
    for (var index = 0; index < glyphs.length; index++) {
      var glyph = glyphs[index];
      if (glyph.isMark) {
        // TODO: handle ligatures
        clusterEnd = index;
      } else {
        if (clusterStart !== clusterEnd) {
          this.positionCluster(glyphs, positions, clusterStart, clusterEnd);
        }
        clusterStart = clusterEnd = index;
      }
    }
    if (clusterStart !== clusterEnd) {
      this.positionCluster(glyphs, positions, clusterStart, clusterEnd);
    }
    return positions;
  };
  _proto17.positionCluster = function positionCluster(glyphs, positions, clusterStart, clusterEnd) {
    var base = glyphs[clusterStart];
    var baseBox = base.cbox.copy(); // adjust bounding box for ligature glyphs

    if (base.codePoints.length > 1) {
      // LTR. TODO: RTL support.
      baseBox.minX += (base.codePoints.length - 1) * baseBox.width / base.codePoints.length;
    }
    var xOffset = -positions[clusterStart].xAdvance;
    var yOffset = 0;
    var yGap = this.font.unitsPerEm / 16; // position each of the mark glyphs relative to the base glyph

    for (var index = clusterStart + 1; index <= clusterEnd; index++) {
      var mark = glyphs[index];
      var markBox = mark.cbox;
      var position = positions[index];
      var combiningClass = this.getCombiningClass(mark.codePoints[0]);
      if (combiningClass !== 'Not_Reordered') {
        position.xOffset = position.yOffset = 0; // x positioning

        switch (combiningClass) {
          case 'Double_Above':
          case 'Double_Below':
            // LTR. TODO: RTL support.
            position.xOffset += baseBox.minX - markBox.width / 2 - markBox.minX;
            break;
          case 'Attached_Below_Left':
          case 'Below_Left':
          case 'Above_Left':
            // left align
            position.xOffset += baseBox.minX - markBox.minX;
            break;
          case 'Attached_Above_Right':
          case 'Below_Right':
          case 'Above_Right':
            // right align
            position.xOffset += baseBox.maxX - markBox.width - markBox.minX;
            break;
          default:
            // Attached_Below, Attached_Above, Below, Above, other
            // center align
            position.xOffset += baseBox.minX + (baseBox.width - markBox.width) / 2 - markBox.minX;
        } // y positioning

        switch (combiningClass) {
          case 'Double_Below':
          case 'Below_Left':
          case 'Below':
          case 'Below_Right':
          case 'Attached_Below_Left':
          case 'Attached_Below':
            // add a small gap between the glyphs if they are not attached
            if (combiningClass === 'Attached_Below_Left' || combiningClass === 'Attached_Below') {
              baseBox.minY += yGap;
            }
            position.yOffset = -baseBox.minY - markBox.maxY;
            baseBox.minY += markBox.height;
            break;
          case 'Double_Above':
          case 'Above_Left':
          case 'Above':
          case 'Above_Right':
          case 'Attached_Above':
          case 'Attached_Above_Right':
            // add a small gap between the glyphs if they are not attached
            if (combiningClass === 'Attached_Above' || combiningClass === 'Attached_Above_Right') {
              baseBox.maxY += yGap;
            }
            position.yOffset = baseBox.maxY - markBox.minY;
            baseBox.maxY += markBox.height;
            break;
        }
        position.xAdvance = position.yAdvance = 0;
        position.xOffset += xOffset;
        position.yOffset += yOffset;
      } else {
        xOffset -= position.xAdvance;
        yOffset -= position.yAdvance;
      }
    }
    return;
  };
  _proto17.getCombiningClass = function getCombiningClass(codePoint) {
    var combiningClass = unicode.getCombiningClass(codePoint); // Thai / Lao need some per-character work

    if ((codePoint & ~0xff) === 0x0e00) {
      if (combiningClass === 'Not_Reordered') {
        switch (codePoint) {
          case 0x0e31:
          case 0x0e34:
          case 0x0e35:
          case 0x0e36:
          case 0x0e37:
          case 0x0e47:
          case 0x0e4c:
          case 0x0e3d:
          case 0x0e4e:
            return 'Above_Right';
          case 0x0eb1:
          case 0x0eb4:
          case 0x0eb5:
          case 0x0eb6:
          case 0x0eb7:
          case 0x0ebb:
          case 0x0ecc:
          case 0x0ecd:
            return 'Above';
          case 0x0ebc:
            return 'Below';
        }
      } else if (codePoint === 0x0e3a) {
        // virama
        return 'Below_Right';
      }
    }
    switch (combiningClass) {
      // Hebrew
      case 'CCC10': // sheva

      case 'CCC11': // hataf segol

      case 'CCC12': // hataf patah

      case 'CCC13': // hataf qamats

      case 'CCC14': // hiriq

      case 'CCC15': // tsere

      case 'CCC16': // segol

      case 'CCC17': // patah

      case 'CCC18': // qamats

      case 'CCC20': // qubuts

      case 'CCC22':
        // meteg
        return 'Below';
      case 'CCC23':
        // rafe
        return 'Attached_Above';
      case 'CCC24':
        // shin dot
        return 'Above_Right';
      case 'CCC25': // sin dot

      case 'CCC19':
        // holam
        return 'Above_Left';
      case 'CCC26':
        // point varika
        return 'Above';
      case 'CCC21':
        // dagesh
        break;
      // Arabic and Syriac

      case 'CCC27': // fathatan

      case 'CCC28': // dammatan

      case 'CCC30': // fatha

      case 'CCC31': // damma

      case 'CCC33': // shadda

      case 'CCC34': // sukun

      case 'CCC35': // superscript alef

      case 'CCC36':
        // superscript alaph
        return 'Above';
      case 'CCC29': // kasratan

      case 'CCC32':
        // kasra
        return 'Below';
      // Thai

      case 'CCC103':
        // sara u / sara uu
        return 'Below_Right';
      case 'CCC107':
        // mai
        return 'Above_Right';
      // Lao

      case 'CCC118':
        // sign u / sign uu
        return 'Below';
      case 'CCC122':
        // mai
        return 'Above';
      // Tibetan

      case 'CCC129': // sign aa

      case 'CCC132':
        // sign u
        return 'Below';
      case 'CCC130':
        // sign i
        return 'Above';
    }
    return combiningClass;
  };
  return UnicodeLayoutEngine;
}();
/**
 * Represents a glyph bounding box
 */
var BBox = /*#__PURE__*/function () {
  function BBox(minX, minY, maxX, maxY) {
    if (minX === void 0) {
      minX = Infinity;
    }
    if (minY === void 0) {
      minY = Infinity;
    }
    if (maxX === void 0) {
      maxX = -Infinity;
    }
    if (maxY === void 0) {
      maxY = -Infinity;
    }
    /**
     * The minimum X position in the bounding box
     * @type {number}
     */
    this.minX = minX;
    /**
     * The minimum Y position in the bounding box
     * @type {number}
     */

    this.minY = minY;
    /**
     * The maxmimum X position in the bounding box
     * @type {number}
     */

    this.maxX = maxX;
    /**
     * The maxmimum Y position in the bounding box
     * @type {number}
     */

    this.maxY = maxY;
  }
  /**
   * The width of the bounding box
   * @type {number}
   */
  var _proto18 = BBox.prototype;
  _proto18.addPoint = function addPoint(x, y) {
    if (Math.abs(x) !== Infinity) {
      if (x < this.minX) {
        this.minX = x;
      }
      if (x > this.maxX) {
        this.maxX = x;
      }
    }
    if (Math.abs(y) !== Infinity) {
      if (y < this.minY) {
        this.minY = y;
      }
      if (y > this.maxY) {
        this.maxY = y;
      }
    }
  };
  _proto18.copy = function copy() {
    return new BBox(this.minX, this.minY, this.maxX, this.maxY);
  };
  return _createClass(BBox, [{
    key: "width",
    get: function get() {
      return this.maxX - this.minX;
    }
    /**
     * The height of the bounding box
     * @type {number}
     */
  }, {
    key: "height",
    get: function get() {
      return this.maxY - this.minY;
    }
  }]);
}(); // Data from http://www.microsoft.com/typography/otspec/scripttags.htm
// and http://www.unicode.org/Public/UNIDATA/PropertyValueAliases.txt.
var UNICODE_SCRIPTS = {
  Caucasian_Albanian: 'aghb',
  Arabic: 'arab',
  Imperial_Aramaic: 'armi',
  Armenian: 'armn',
  Avestan: 'avst',
  Balinese: 'bali',
  Bamum: 'bamu',
  Bassa_Vah: 'bass',
  Batak: 'batk',
  Bengali: ['bng2', 'beng'],
  Bopomofo: 'bopo',
  Brahmi: 'brah',
  Braille: 'brai',
  Buginese: 'bugi',
  Buhid: 'buhd',
  Chakma: 'cakm',
  Canadian_Aboriginal: 'cans',
  Carian: 'cari',
  Cham: 'cham',
  Cherokee: 'cher',
  Coptic: 'copt',
  Cypriot: 'cprt',
  Cyrillic: 'cyrl',
  Devanagari: ['dev2', 'deva'],
  Deseret: 'dsrt',
  Duployan: 'dupl',
  Egyptian_Hieroglyphs: 'egyp',
  Elbasan: 'elba',
  Ethiopic: 'ethi',
  Georgian: 'geor',
  Glagolitic: 'glag',
  Gothic: 'goth',
  Grantha: 'gran',
  Greek: 'grek',
  Gujarati: ['gjr2', 'gujr'],
  Gurmukhi: ['gur2', 'guru'],
  Hangul: 'hang',
  Han: 'hani',
  Hanunoo: 'hano',
  Hebrew: 'hebr',
  Hiragana: 'hira',
  Pahawh_Hmong: 'hmng',
  Katakana_Or_Hiragana: 'hrkt',
  Old_Italic: 'ital',
  Javanese: 'java',
  Kayah_Li: 'kali',
  Katakana: 'kana',
  Kharoshthi: 'khar',
  Khmer: 'khmr',
  Khojki: 'khoj',
  Kannada: ['knd2', 'knda'],
  Kaithi: 'kthi',
  Tai_Tham: 'lana',
  Lao: 'lao ',
  Latin: 'latn',
  Lepcha: 'lepc',
  Limbu: 'limb',
  Linear_A: 'lina',
  Linear_B: 'linb',
  Lisu: 'lisu',
  Lycian: 'lyci',
  Lydian: 'lydi',
  Mahajani: 'mahj',
  Mandaic: 'mand',
  Manichaean: 'mani',
  Mende_Kikakui: 'mend',
  Meroitic_Cursive: 'merc',
  Meroitic_Hieroglyphs: 'mero',
  Malayalam: ['mlm2', 'mlym'],
  Modi: 'modi',
  Mongolian: 'mong',
  Mro: 'mroo',
  Meetei_Mayek: 'mtei',
  Myanmar: ['mym2', 'mymr'],
  Old_North_Arabian: 'narb',
  Nabataean: 'nbat',
  Nko: 'nko ',
  Ogham: 'ogam',
  Ol_Chiki: 'olck',
  Old_Turkic: 'orkh',
  Oriya: ['ory2', 'orya'],
  Osmanya: 'osma',
  Palmyrene: 'palm',
  Pau_Cin_Hau: 'pauc',
  Old_Permic: 'perm',
  Phags_Pa: 'phag',
  Inscriptional_Pahlavi: 'phli',
  Psalter_Pahlavi: 'phlp',
  Phoenician: 'phnx',
  Miao: 'plrd',
  Inscriptional_Parthian: 'prti',
  Rejang: 'rjng',
  Runic: 'runr',
  Samaritan: 'samr',
  Old_South_Arabian: 'sarb',
  Saurashtra: 'saur',
  Shavian: 'shaw',
  Sharada: 'shrd',
  Siddham: 'sidd',
  Khudawadi: 'sind',
  Sinhala: 'sinh',
  Sora_Sompeng: 'sora',
  Sundanese: 'sund',
  Syloti_Nagri: 'sylo',
  Syriac: 'syrc',
  Tagbanwa: 'tagb',
  Takri: 'takr',
  Tai_Le: 'tale',
  New_Tai_Lue: 'talu',
  Tamil: ['tml2', 'taml'],
  Tai_Viet: 'tavt',
  Telugu: ['tel2', 'telu'],
  Tifinagh: 'tfng',
  Tagalog: 'tglg',
  Thaana: 'thaa',
  Thai: 'thai',
  Tibetan: 'tibt',
  Tirhuta: 'tirh',
  Ugaritic: 'ugar',
  Vai: 'vai ',
  Warang_Citi: 'wara',
  Old_Persian: 'xpeo',
  Cuneiform: 'xsux',
  Yi: 'yi  ',
  Inherited: 'zinh',
  Common: 'zyyy',
  Unknown: 'zzzz'
};
var OPENTYPE_SCRIPTS = {};
for (var script in UNICODE_SCRIPTS) {
  var tag = UNICODE_SCRIPTS[script];
  if (Array.isArray(tag)) {
    for (var _iterator17 = _createForOfIteratorHelperLoose(tag), _step17; !(_step17 = _iterator17()).done;) {
      var t = _step17.value;
      OPENTYPE_SCRIPTS[t] = script;
    }
  } else {
    OPENTYPE_SCRIPTS[tag] = script;
  }
}
function fromOpenType(tag) {
  return OPENTYPE_SCRIPTS[tag];
}
function forString(string) {
  var len = string.length;
  var idx = 0;
  while (idx < len) {
    var code = string.charCodeAt(idx++); // Check if this is a high surrogate

    if (0xd800 <= code && code <= 0xdbff && idx < len) {
      var next = string.charCodeAt(idx); // Check if this is a low surrogate

      if (0xdc00 <= next && next <= 0xdfff) {
        idx++;
        code = ((code & 0x3FF) << 10) + (next & 0x3FF) + 0x10000;
      }
    }
    var _script = unicode.getScript(code);
    if (_script !== 'Common' && _script !== 'Inherited' && _script !== 'Unknown') {
      return UNICODE_SCRIPTS[_script];
    }
  }
  return UNICODE_SCRIPTS.Unknown;
}
function forCodePoints(codePoints) {
  for (var i = 0; i < codePoints.length; i++) {
    var codePoint = codePoints[i];
    var _script2 = unicode.getScript(codePoint);
    if (_script2 !== 'Common' && _script2 !== 'Inherited' && _script2 !== 'Unknown') {
      return UNICODE_SCRIPTS[_script2];
    }
  }
  return UNICODE_SCRIPTS.Unknown;
} // The scripts in this map are written from right to left

var RTL = {
  arab: true,
  // Arabic
  hebr: true,
  // Hebrew
  syrc: true,
  // Syriac
  thaa: true,
  // Thaana
  cprt: true,
  // Cypriot Syllabary
  khar: true,
  // Kharosthi
  phnx: true,
  // Phoenician
  'nko ': true,
  // N'Ko
  lydi: true,
  // Lydian
  avst: true,
  // Avestan
  armi: true,
  // Imperial Aramaic
  phli: true,
  // Inscriptional Pahlavi
  prti: true,
  // Inscriptional Parthian
  sarb: true,
  // Old South Arabian
  orkh: true,
  // Old Turkic, Orkhon Runic
  samr: true,
  // Samaritan
  mand: true,
  // Mandaic, Mandaean
  merc: true,
  // Meroitic Cursive
  mero: true,
  // Meroitic Hieroglyphs
  // Unicode 7.0 (not listed on http://www.microsoft.com/typography/otspec/scripttags.htm)
  mani: true,
  // Manichaean
  mend: true,
  // Mende Kikakui
  nbat: true,
  // Nabataean
  narb: true,
  // Old North Arabian
  palm: true,
  // Palmyrene
  phlp: true // Psalter Pahlavi
};
function direction(script) {
  if (RTL[script]) {
    return 'rtl';
  }
  return 'ltr';
}

/**
 * Represents a run of Glyph and GlyphPosition objects.
 * Returned by the font layout method.
 */
var GlyphRun = /*#__PURE__*/function () {
  function GlyphRun(glyphs, features, script, language, direction$1) {
    /**
     * An array of Glyph objects in the run
     * @type {Glyph[]}
     */
    this.glyphs = glyphs;
    /**
     * An array of GlyphPosition objects for each glyph in the run
     * @type {GlyphPosition[]}
     */

    this.positions = null;
    /**
     * The script that was requested for shaping. This was either passed in or detected automatically.
     * @type {string}
     */

    this.script = script;
    /**
     * The language requested for shaping, as passed in. If `null`, the default language for the
     * script was used.
     * @type {string}
     */

    this.language = language || null;
    /**
     * The direction requested for shaping, as passed in (either ltr or rtl).
     * If `null`, the default direction of the script is used.
     * @type {string}
     */

    this.direction = direction$1 || direction(script);
    /**
     * The features requested during shaping. This is a combination of user
     * specified features and features chosen by the shaper.
     * @type {object}
     */

    this.features = {}; // Convert features to an object

    if (Array.isArray(features)) {
      for (var _iterator18 = _createForOfIteratorHelperLoose(features), _step18; !(_step18 = _iterator18()).done;) {
        var tag = _step18.value;
        this.features[tag] = true;
      }
    } else if (typeof features === 'object') {
      this.features = features;
    }
  }
  /**
   * The total advance width of the run.
   * @type {number}
   */
  return _createClass(GlyphRun, [{
    key: "advanceWidth",
    get: function get() {
      var width = 0;
      for (var _iterator19 = _createForOfIteratorHelperLoose(this.positions), _step19; !(_step19 = _iterator19()).done;) {
        var position = _step19.value;
        width += position.xAdvance;
      }
      return width;
    }
    /**
     * The total advance height of the run.
     * @type {number}
     */
  }, {
    key: "advanceHeight",
    get: function get() {
      var height = 0;
      for (var _iterator20 = _createForOfIteratorHelperLoose(this.positions), _step20; !(_step20 = _iterator20()).done;) {
        var position = _step20.value;
        height += position.yAdvance;
      }
      return height;
    }
    /**
     * The bounding box containing all glyphs in the run.
     * @type {BBox}
     */
  }, {
    key: "bbox",
    get: function get() {
      var bbox = new BBox();
      var x = 0;
      var y = 0;
      for (var index = 0; index < this.glyphs.length; index++) {
        var glyph = this.glyphs[index];
        var p = this.positions[index];
        var b = glyph.bbox;
        bbox.addPoint(b.minX + x + p.xOffset, b.minY + y + p.yOffset);
        bbox.addPoint(b.maxX + x + p.xOffset, b.maxY + y + p.yOffset);
        x += p.xAdvance;
        y += p.yAdvance;
      }
      return bbox;
    }
  }]);
}();
/**
 * Represents positioning information for a glyph in a GlyphRun.
 */
var GlyphPosition = function GlyphPosition(xAdvance, yAdvance, xOffset, yOffset) {
  if (xAdvance === void 0) {
    xAdvance = 0;
  }
  if (yAdvance === void 0) {
    yAdvance = 0;
  }
  if (xOffset === void 0) {
    xOffset = 0;
  }
  if (yOffset === void 0) {
    yOffset = 0;
  }
  /**
   * The amount to move the virtual pen in the X direction after rendering this glyph.
   * @type {number}
   */
  this.xAdvance = xAdvance;
  /**
   * The amount to move the virtual pen in the Y direction after rendering this glyph.
   * @type {number}
   */

  this.yAdvance = yAdvance;
  /**
   * The offset from the pen position in the X direction at which to render this glyph.
   * @type {number}
   */

  this.xOffset = xOffset;
  /**
   * The offset from the pen position in the Y direction at which to render this glyph.
   * @type {number}
   */

  this.yOffset = yOffset;
}; // see https://developer.apple.com/fonts/TrueType-Reference-Manual/RM09/AppendixF.html
// and /System/Library/Frameworks/CoreText.framework/Versions/A/Headers/SFNTLayoutTypes.h on a Mac
var features = {
  allTypographicFeatures: {
    code: 0,
    exclusive: false,
    allTypeFeatures: 0
  },
  ligatures: {
    code: 1,
    exclusive: false,
    requiredLigatures: 0,
    commonLigatures: 2,
    rareLigatures: 4,
    // logos: 6
    rebusPictures: 8,
    diphthongLigatures: 10,
    squaredLigatures: 12,
    abbrevSquaredLigatures: 14,
    symbolLigatures: 16,
    contextualLigatures: 18,
    historicalLigatures: 20
  },
  cursiveConnection: {
    code: 2,
    exclusive: true,
    unconnected: 0,
    partiallyConnected: 1,
    cursive: 2
  },
  letterCase: {
    code: 3,
    exclusive: true
  },
  // upperAndLowerCase: 0          # deprecated
  // allCaps: 1                    # deprecated
  // allLowerCase: 2               # deprecated
  // smallCaps: 3                  # deprecated
  // initialCaps: 4                # deprecated
  // initialCapsAndSmallCaps: 5    # deprecated
  verticalSubstitution: {
    code: 4,
    exclusive: false,
    substituteVerticalForms: 0
  },
  linguisticRearrangement: {
    code: 5,
    exclusive: false,
    linguisticRearrangement: 0
  },
  numberSpacing: {
    code: 6,
    exclusive: true,
    monospacedNumbers: 0,
    proportionalNumbers: 1,
    thirdWidthNumbers: 2,
    quarterWidthNumbers: 3
  },
  smartSwash: {
    code: 8,
    exclusive: false,
    wordInitialSwashes: 0,
    wordFinalSwashes: 2,
    // lineInitialSwashes: 4
    // lineFinalSwashes: 6
    nonFinalSwashes: 8
  },
  diacritics: {
    code: 9,
    exclusive: true,
    showDiacritics: 0,
    hideDiacritics: 1,
    decomposeDiacritics: 2
  },
  verticalPosition: {
    code: 10,
    exclusive: true,
    normalPosition: 0,
    superiors: 1,
    inferiors: 2,
    ordinals: 3,
    scientificInferiors: 4
  },
  fractions: {
    code: 11,
    exclusive: true,
    noFractions: 0,
    verticalFractions: 1,
    diagonalFractions: 2
  },
  overlappingCharacters: {
    code: 13,
    exclusive: false,
    preventOverlap: 0
  },
  typographicExtras: {
    code: 14,
    exclusive: false,
    // hyphensToEmDash: 0
    // hyphenToEnDash: 2
    slashedZero: 4
  },
  // formInterrobang: 6
  // smartQuotes: 8
  // periodsToEllipsis: 10
  mathematicalExtras: {
    code: 15,
    exclusive: false,
    // hyphenToMinus: 0
    // asteristoMultiply: 2
    // slashToDivide: 4
    // inequalityLigatures: 6
    // exponents: 8
    mathematicalGreek: 10
  },
  ornamentSets: {
    code: 16,
    exclusive: true,
    noOrnaments: 0,
    dingbats: 1,
    piCharacters: 2,
    fleurons: 3,
    decorativeBorders: 4,
    internationalSymbols: 5,
    mathSymbols: 6
  },
  characterAlternatives: {
    code: 17,
    exclusive: true,
    noAlternates: 0
  },
  // user defined options
  designComplexity: {
    code: 18,
    exclusive: true,
    designLevel1: 0,
    designLevel2: 1,
    designLevel3: 2,
    designLevel4: 3,
    designLevel5: 4
  },
  styleOptions: {
    code: 19,
    exclusive: true,
    noStyleOptions: 0,
    displayText: 1,
    engravedText: 2,
    illuminatedCaps: 3,
    titlingCaps: 4,
    tallCaps: 5
  },
  characterShape: {
    code: 20,
    exclusive: true,
    traditionalCharacters: 0,
    simplifiedCharacters: 1,
    JIS1978Characters: 2,
    JIS1983Characters: 3,
    JIS1990Characters: 4,
    traditionalAltOne: 5,
    traditionalAltTwo: 6,
    traditionalAltThree: 7,
    traditionalAltFour: 8,
    traditionalAltFive: 9,
    expertCharacters: 10,
    JIS2004Characters: 11,
    hojoCharacters: 12,
    NLCCharacters: 13,
    traditionalNamesCharacters: 14
  },
  numberCase: {
    code: 21,
    exclusive: true,
    lowerCaseNumbers: 0,
    upperCaseNumbers: 1
  },
  textSpacing: {
    code: 22,
    exclusive: true,
    proportionalText: 0,
    monospacedText: 1,
    halfWidthText: 2,
    thirdWidthText: 3,
    quarterWidthText: 4,
    altProportionalText: 5,
    altHalfWidthText: 6
  },
  transliteration: {
    code: 23,
    exclusive: true,
    noTransliteration: 0
  },
  // hanjaToHangul: 1
  // hiraganaToKatakana: 2
  // katakanaToHiragana: 3
  // kanaToRomanization: 4
  // romanizationToHiragana: 5
  // romanizationToKatakana: 6
  // hanjaToHangulAltOne: 7
  // hanjaToHangulAltTwo: 8
  // hanjaToHangulAltThree: 9
  annotation: {
    code: 24,
    exclusive: true,
    noAnnotation: 0,
    boxAnnotation: 1,
    roundedBoxAnnotation: 2,
    circleAnnotation: 3,
    invertedCircleAnnotation: 4,
    parenthesisAnnotation: 5,
    periodAnnotation: 6,
    romanNumeralAnnotation: 7,
    diamondAnnotation: 8,
    invertedBoxAnnotation: 9,
    invertedRoundedBoxAnnotation: 10
  },
  kanaSpacing: {
    code: 25,
    exclusive: true,
    fullWidthKana: 0,
    proportionalKana: 1
  },
  ideographicSpacing: {
    code: 26,
    exclusive: true,
    fullWidthIdeographs: 0,
    proportionalIdeographs: 1,
    halfWidthIdeographs: 2
  },
  unicodeDecomposition: {
    code: 27,
    exclusive: false,
    canonicalComposition: 0,
    compatibilityComposition: 2,
    transcodingComposition: 4
  },
  rubyKana: {
    code: 28,
    exclusive: false,
    // noRubyKana: 0     # deprecated - use rubyKanaOff instead
    // rubyKana: 1     # deprecated - use rubyKanaOn instead
    rubyKana: 2
  },
  CJKSymbolAlternatives: {
    code: 29,
    exclusive: true,
    noCJKSymbolAlternatives: 0,
    CJKSymbolAltOne: 1,
    CJKSymbolAltTwo: 2,
    CJKSymbolAltThree: 3,
    CJKSymbolAltFour: 4,
    CJKSymbolAltFive: 5
  },
  ideographicAlternatives: {
    code: 30,
    exclusive: true,
    noIdeographicAlternatives: 0,
    ideographicAltOne: 1,
    ideographicAltTwo: 2,
    ideographicAltThree: 3,
    ideographicAltFour: 4,
    ideographicAltFive: 5
  },
  CJKVerticalRomanPlacement: {
    code: 31,
    exclusive: true,
    CJKVerticalRomanCentered: 0,
    CJKVerticalRomanHBaseline: 1
  },
  italicCJKRoman: {
    code: 32,
    exclusive: false,
    // noCJKItalicRoman: 0     # deprecated - use CJKItalicRomanOff instead
    // CJKItalicRoman: 1     # deprecated - use CJKItalicRomanOn instead
    CJKItalicRoman: 2
  },
  caseSensitiveLayout: {
    code: 33,
    exclusive: false,
    caseSensitiveLayout: 0,
    caseSensitiveSpacing: 2
  },
  alternateKana: {
    code: 34,
    exclusive: false,
    alternateHorizKana: 0,
    alternateVertKana: 2
  },
  stylisticAlternatives: {
    code: 35,
    exclusive: false,
    noStylisticAlternates: 0,
    stylisticAltOne: 2,
    stylisticAltTwo: 4,
    stylisticAltThree: 6,
    stylisticAltFour: 8,
    stylisticAltFive: 10,
    stylisticAltSix: 12,
    stylisticAltSeven: 14,
    stylisticAltEight: 16,
    stylisticAltNine: 18,
    stylisticAltTen: 20,
    stylisticAltEleven: 22,
    stylisticAltTwelve: 24,
    stylisticAltThirteen: 26,
    stylisticAltFourteen: 28,
    stylisticAltFifteen: 30,
    stylisticAltSixteen: 32,
    stylisticAltSeventeen: 34,
    stylisticAltEighteen: 36,
    stylisticAltNineteen: 38,
    stylisticAltTwenty: 40
  },
  contextualAlternates: {
    code: 36,
    exclusive: false,
    contextualAlternates: 0,
    swashAlternates: 2,
    contextualSwashAlternates: 4
  },
  lowerCase: {
    code: 37,
    exclusive: true,
    defaultLowerCase: 0,
    lowerCaseSmallCaps: 1,
    lowerCasePetiteCaps: 2
  },
  upperCase: {
    code: 38,
    exclusive: true,
    defaultUpperCase: 0,
    upperCaseSmallCaps: 1,
    upperCasePetiteCaps: 2
  },
  languageTag: {
    // indices into ltag table
    code: 39,
    exclusive: true
  },
  CJKRomanSpacing: {
    code: 103,
    exclusive: true,
    halfWidthCJKRoman: 0,
    proportionalCJKRoman: 1,
    defaultCJKRoman: 2,
    fullWidthCJKRoman: 3
  }
};
var feature = function feature(name, selector) {
  return [features[name].code, features[name][selector]];
};
var OTMapping = {
  rlig: feature('ligatures', 'requiredLigatures'),
  clig: feature('ligatures', 'contextualLigatures'),
  dlig: feature('ligatures', 'rareLigatures'),
  hlig: feature('ligatures', 'historicalLigatures'),
  liga: feature('ligatures', 'commonLigatures'),
  hist: feature('ligatures', 'historicalLigatures'),
  // ??
  smcp: feature('lowerCase', 'lowerCaseSmallCaps'),
  pcap: feature('lowerCase', 'lowerCasePetiteCaps'),
  frac: feature('fractions', 'diagonalFractions'),
  dnom: feature('fractions', 'diagonalFractions'),
  // ??
  numr: feature('fractions', 'diagonalFractions'),
  // ??
  afrc: feature('fractions', 'verticalFractions'),
  // aalt
  // abvf, abvm, abvs, akhn, blwf, blwm, blws, cfar, cjct, cpsp, falt, isol, jalt, ljmo, mset?
  // ltra, ltrm, nukt, pref, pres, pstf, psts, rand, rkrf, rphf, rtla, rtlm, size, tjmo, tnum?
  // unic, vatu, vhal, vjmo, vpal, vrt2
  // dist -> trak table?
  // kern, vkrn -> kern table
  // lfbd + opbd + rtbd -> opbd table?
  // mark, mkmk -> acnt table?
  // locl -> languageTag + ltag table
  case: feature('caseSensitiveLayout', 'caseSensitiveLayout'),
  // also caseSensitiveSpacing
  ccmp: feature('unicodeDecomposition', 'canonicalComposition'),
  // compatibilityComposition?
  cpct: feature('CJKVerticalRomanPlacement', 'CJKVerticalRomanCentered'),
  // guess..., probably not given below
  valt: feature('CJKVerticalRomanPlacement', 'CJKVerticalRomanCentered'),
  swsh: feature('contextualAlternates', 'swashAlternates'),
  cswh: feature('contextualAlternates', 'contextualSwashAlternates'),
  curs: feature('cursiveConnection', 'cursive'),
  // ??
  c2pc: feature('upperCase', 'upperCasePetiteCaps'),
  c2sc: feature('upperCase', 'upperCaseSmallCaps'),
  init: feature('smartSwash', 'wordInitialSwashes'),
  // ??
  fin2: feature('smartSwash', 'wordFinalSwashes'),
  // ??
  medi: feature('smartSwash', 'nonFinalSwashes'),
  // ??
  med2: feature('smartSwash', 'nonFinalSwashes'),
  // ??
  fin3: feature('smartSwash', 'wordFinalSwashes'),
  // ??
  fina: feature('smartSwash', 'wordFinalSwashes'),
  // ??
  pkna: feature('kanaSpacing', 'proportionalKana'),
  half: feature('textSpacing', 'halfWidthText'),
  // also HalfWidthCJKRoman, HalfWidthIdeographs?
  halt: feature('textSpacing', 'altHalfWidthText'),
  hkna: feature('alternateKana', 'alternateHorizKana'),
  vkna: feature('alternateKana', 'alternateVertKana'),
  // hngl: feature 'transliteration', 'hanjaToHangulSelector' # deprecated
  ital: feature('italicCJKRoman', 'CJKItalicRoman'),
  lnum: feature('numberCase', 'upperCaseNumbers'),
  onum: feature('numberCase', 'lowerCaseNumbers'),
  mgrk: feature('mathematicalExtras', 'mathematicalGreek'),
  // nalt: not enough info. what type of annotation?
  // ornm: ditto, which ornament style?
  calt: feature('contextualAlternates', 'contextualAlternates'),
  // or more?
  vrt2: feature('verticalSubstitution', 'substituteVerticalForms'),
  // oh... below?
  vert: feature('verticalSubstitution', 'substituteVerticalForms'),
  tnum: feature('numberSpacing', 'monospacedNumbers'),
  pnum: feature('numberSpacing', 'proportionalNumbers'),
  sups: feature('verticalPosition', 'superiors'),
  subs: feature('verticalPosition', 'inferiors'),
  ordn: feature('verticalPosition', 'ordinals'),
  pwid: feature('textSpacing', 'proportionalText'),
  hwid: feature('textSpacing', 'halfWidthText'),
  qwid: feature('textSpacing', 'quarterWidthText'),
  // also QuarterWidthNumbers?
  twid: feature('textSpacing', 'thirdWidthText'),
  // also ThirdWidthNumbers?
  fwid: feature('textSpacing', 'proportionalText'),
  //??
  palt: feature('textSpacing', 'altProportionalText'),
  trad: feature('characterShape', 'traditionalCharacters'),
  smpl: feature('characterShape', 'simplifiedCharacters'),
  jp78: feature('characterShape', 'JIS1978Characters'),
  jp83: feature('characterShape', 'JIS1983Characters'),
  jp90: feature('characterShape', 'JIS1990Characters'),
  jp04: feature('characterShape', 'JIS2004Characters'),
  expt: feature('characterShape', 'expertCharacters'),
  hojo: feature('characterShape', 'hojoCharacters'),
  nlck: feature('characterShape', 'NLCCharacters'),
  tnam: feature('characterShape', 'traditionalNamesCharacters'),
  ruby: feature('rubyKana', 'rubyKana'),
  titl: feature('styleOptions', 'titlingCaps'),
  zero: feature('typographicExtras', 'slashedZero'),
  ss01: feature('stylisticAlternatives', 'stylisticAltOne'),
  ss02: feature('stylisticAlternatives', 'stylisticAltTwo'),
  ss03: feature('stylisticAlternatives', 'stylisticAltThree'),
  ss04: feature('stylisticAlternatives', 'stylisticAltFour'),
  ss05: feature('stylisticAlternatives', 'stylisticAltFive'),
  ss06: feature('stylisticAlternatives', 'stylisticAltSix'),
  ss07: feature('stylisticAlternatives', 'stylisticAltSeven'),
  ss08: feature('stylisticAlternatives', 'stylisticAltEight'),
  ss09: feature('stylisticAlternatives', 'stylisticAltNine'),
  ss10: feature('stylisticAlternatives', 'stylisticAltTen'),
  ss11: feature('stylisticAlternatives', 'stylisticAltEleven'),
  ss12: feature('stylisticAlternatives', 'stylisticAltTwelve'),
  ss13: feature('stylisticAlternatives', 'stylisticAltThirteen'),
  ss14: feature('stylisticAlternatives', 'stylisticAltFourteen'),
  ss15: feature('stylisticAlternatives', 'stylisticAltFifteen'),
  ss16: feature('stylisticAlternatives', 'stylisticAltSixteen'),
  ss17: feature('stylisticAlternatives', 'stylisticAltSeventeen'),
  ss18: feature('stylisticAlternatives', 'stylisticAltEighteen'),
  ss19: feature('stylisticAlternatives', 'stylisticAltNineteen'),
  ss20: feature('stylisticAlternatives', 'stylisticAltTwenty')
}; // salt: feature 'stylisticAlternatives', 'stylisticAltOne' # hmm, which one to choose
// Add cv01-cv99 features

for (var i = 1; i <= 99; i++) {
  OTMapping["cv".concat("00".concat(i).slice(-2))] = [features.characterAlternatives.code, i];
} // create inverse mapping

var AATMapping = {};
for (var ot in OTMapping) {
  var aat = OTMapping[ot];
  if (AATMapping[aat[0]] == null) {
    AATMapping[aat[0]] = {};
  }
  AATMapping[aat[0]][aat[1]] = ot;
} // Maps an array of OpenType features to AAT features
// in the form of {featureType:{featureSetting:true}}

function mapOTToAAT(features) {
  var res = {};
  for (var k in features) {
    var r = void 0;
    if (r = OTMapping[k]) {
      if (res[r[0]] == null) {
        res[r[0]] = {};
      }
      res[r[0]][r[1]] = features[k];
    }
  }
  return res;
} // Maps strings in a [featureType, featureSetting]
// to their equivalent number codes

function mapFeatureStrings(f) {
  var type = f[0],
    setting = f[1];
  if (isNaN(type)) {
    var typeCode = features[type] && features[type].code;
  } else {
    var typeCode = type;
  }
  if (isNaN(setting)) {
    var settingCode = features[type] && features[type][setting];
  } else {
    var settingCode = setting;
  }
  return [typeCode, settingCode];
} // Maps AAT features to an array of OpenType features
// Supports both arrays in the form of [[featureType, featureSetting]]
// and objects in the form of {featureType:{featureSetting:true}}
// featureTypes and featureSettings can be either strings or number codes

function mapAATToOT(features) {
  var res = {};
  if (Array.isArray(features)) {
    for (var k = 0; k < features.length; k++) {
      var r = void 0;
      var f = mapFeatureStrings(features[k]);
      if (r = AATMapping[f[0]] && AATMapping[f[0]][f[1]]) {
        res[r] = true;
      }
    }
  } else if (typeof features === 'object') {
    for (var type in features) {
      var _feature = features[type];
      for (var setting in _feature) {
        var _r = void 0;
        var _f = mapFeatureStrings([type, setting]);
        if (_feature[setting] && (_r = AATMapping[_f[0]] && AATMapping[_f[0]][_f[1]])) {
          res[_r] = true;
        }
      }
    }
  }
  return Object.keys(res);
}
var _class$1;
var AATLookupTable = (_class$1 = /*#__PURE__*/function () {
  function AATLookupTable(table) {
    this.table = table;
  }
  var _proto19 = AATLookupTable.prototype;
  _proto19.lookup = function lookup(glyph) {
    switch (this.table.version) {
      case 0:
        // simple array format
        return this.table.values.getItem(glyph);
      case 2: // segment format

      case 4:
        {
          var min = 0;
          var max = this.table.binarySearchHeader.nUnits - 1;
          while (min <= max) {
            var mid = min + max >> 1;
            var seg = this.table.segments[mid]; // special end of search value

            if (seg.firstGlyph === 0xffff) {
              return null;
            }
            if (glyph < seg.firstGlyph) {
              max = mid - 1;
            } else if (glyph > seg.lastGlyph) {
              min = mid + 1;
            } else {
              if (this.table.version === 2) {
                return seg.value;
              } else {
                return seg.values[glyph - seg.firstGlyph];
              }
            }
          }
          return null;
        }
      case 6:
        {
          // lookup single
          var _min = 0;
          var _max = this.table.binarySearchHeader.nUnits - 1;
          while (_min <= _max) {
            var mid = _min + _max >> 1;
            var seg = this.table.segments[mid]; // special end of search value

            if (seg.glyph === 0xffff) {
              return null;
            }
            if (glyph < seg.glyph) {
              _max = mid - 1;
            } else if (glyph > seg.glyph) {
              _min = mid + 1;
            } else {
              return seg.value;
            }
          }
          return null;
        }
      case 8:
        // lookup trimmed
        return this.table.values[glyph - this.table.firstGlyph];
      default:
        throw new Error("Unknown lookup table format: ".concat(this.table.version));
    }
  };
  _proto19.glyphsForValue = function glyphsForValue(classValue) {
    var res = [];
    switch (this.table.version) {
      case 2: // segment format

      case 4:
        {
          for (var _iterator21 = _createForOfIteratorHelperLoose(this.table.segments), _step21; !(_step21 = _iterator21()).done;) {
            var segment = _step21.value;
            if (this.table.version === 2 && segment.value === classValue) {
              res.push.apply(res, range(segment.firstGlyph, segment.lastGlyph + 1));
            } else {
              for (var index = 0; index < segment.values.length; index++) {
                if (segment.values[index] === classValue) {
                  res.push(segment.firstGlyph + index);
                }
              }
            }
          }
          break;
        }
      case 6:
        {
          // lookup single
          for (var _iterator22 = _createForOfIteratorHelperLoose(this.table.segments), _step22; !(_step22 = _iterator22()).done;) {
            var _segment = _step22.value;
            if (_segment.value === classValue) {
              res.push(_segment.glyph);
            }
          }
          break;
        }
      case 8:
        {
          // lookup trimmed
          for (var i = 0; i < this.table.values.length; i++) {
            if (this.table.values[i] === classValue) {
              res.push(this.table.firstGlyph + i);
            }
          }
          break;
        }
      default:
        throw new Error("Unknown lookup table format: ".concat(this.table.version));
    }
    return res;
  };
  return AATLookupTable;
}(), _applyDecoratedDescriptor(_class$1.prototype, "glyphsForValue", [cache], Object.getOwnPropertyDescriptor(_class$1.prototype, "glyphsForValue"), _class$1.prototype), _class$1);
var START_OF_TEXT_STATE = 0;
var END_OF_TEXT_CLASS = 0;
var OUT_OF_BOUNDS_CLASS = 1;
var DELETED_GLYPH_CLASS = 2;
var DONT_ADVANCE = 0x4000;
var AATStateMachine = /*#__PURE__*/function () {
  function AATStateMachine(stateTable) {
    this.stateTable = stateTable;
    this.lookupTable = new AATLookupTable(stateTable.classTable);
  }
  var _proto20 = AATStateMachine.prototype;
  _proto20.process = function process(glyphs, reverse, processEntry) {
    var currentState = START_OF_TEXT_STATE; // START_OF_LINE_STATE is used for kashida glyph insertions sometimes I think?

    var index = reverse ? glyphs.length - 1 : 0;
    var dir = reverse ? -1 : 1;
    while (dir === 1 && index <= glyphs.length || dir === -1 && index >= -1) {
      var glyph = null;
      var classCode = OUT_OF_BOUNDS_CLASS;
      var shouldAdvance = true;
      if (index === glyphs.length || index === -1) {
        classCode = END_OF_TEXT_CLASS;
      } else {
        glyph = glyphs[index];
        if (glyph.id === 0xffff) {
          // deleted glyph
          classCode = DELETED_GLYPH_CLASS;
        } else {
          classCode = this.lookupTable.lookup(glyph.id);
          if (classCode == null) {
            classCode = OUT_OF_BOUNDS_CLASS;
          }
        }
      }
      var row = this.stateTable.stateArray.getItem(currentState);
      var entryIndex = row[classCode];
      var entry = this.stateTable.entryTable.getItem(entryIndex);
      if (classCode !== END_OF_TEXT_CLASS && classCode !== DELETED_GLYPH_CLASS) {
        processEntry(glyph, entry, index);
        shouldAdvance = !(entry.flags & DONT_ADVANCE);
      }
      currentState = entry.newState;
      if (shouldAdvance) {
        index += dir;
      }
    }
    return glyphs;
  }
  /**
   * Performs a depth-first traversal of the glyph strings
   * represented by the state machine.
   */;
  _proto20.traverse = function traverse(opts, state, visited) {
    if (state === void 0) {
      state = 0;
    }
    if (visited === void 0) {
      visited = new Set();
    }
    if (visited.has(state)) {
      return;
    }
    visited.add(state);
    var _this$stateTable = this.stateTable,
      nClasses = _this$stateTable.nClasses,
      stateArray = _this$stateTable.stateArray,
      entryTable = _this$stateTable.entryTable;
    var row = stateArray.getItem(state); // Skip predefined classes

    for (var classCode = 4; classCode < nClasses; classCode++) {
      var entryIndex = row[classCode];
      var entry = entryTable.getItem(entryIndex); // Try all glyphs in the class

      for (var _iterator23 = _createForOfIteratorHelperLoose(this.lookupTable.glyphsForValue(classCode)), _step23; !(_step23 = _iterator23()).done;) {
        var glyph = _step23.value;
        if (opts.enter) {
          opts.enter(glyph, entry);
        }
        if (entry.newState !== 0) {
          this.traverse(opts, entry.newState, visited);
        }
        if (opts.exit) {
          opts.exit(glyph, entry);
        }
      }
    }
  };
  return AATStateMachine;
}();
var _class$2;
var MARK_FIRST = 0x8000;
var MARK_LAST = 0x2000;
var VERB = 0x000F; // contextual substitution and glyph insertion flag

var SET_MARK = 0x8000; // ligature entry flags

var SET_COMPONENT = 0x8000;
var PERFORM_ACTION = 0x2000; // ligature action masks

var LAST_MASK = 0x80000000;
var STORE_MASK = 0x40000000;
var OFFSET_MASK = 0x3FFFFFFF;
var REVERSE_DIRECTION = 0x400000;
var CURRENT_INSERT_BEFORE = 0x0800;
var MARKED_INSERT_BEFORE = 0x0400;
var CURRENT_INSERT_COUNT = 0x03E0;
var MARKED_INSERT_COUNT = 0x001F;
var AATMorxProcessor = (_class$2 = /*#__PURE__*/function () {
  function AATMorxProcessor(font) {
    this.processIndicRearragement = this.processIndicRearragement.bind(this);
    this.processContextualSubstitution = this.processContextualSubstitution.bind(this);
    this.processLigature = this.processLigature.bind(this);
    this.processNoncontextualSubstitutions = this.processNoncontextualSubstitutions.bind(this);
    this.processGlyphInsertion = this.processGlyphInsertion.bind(this);
    this.font = font;
    this.morx = font.morx;
    this.inputCache = null;
  } // Processes an array of glyphs and applies the specified features
  // Features should be in the form of {featureType:{featureSetting:boolean}}
  var _proto21 = AATMorxProcessor.prototype;
  _proto21.process = function process(glyphs, features) {
    if (features === void 0) {
      features = {};
    }
    for (var _iterator24 = _createForOfIteratorHelperLoose(this.morx.chains), _step24; !(_step24 = _iterator24()).done;) {
      var chain = _step24.value;
      var flags = chain.defaultFlags; // enable/disable the requested features

      for (var _iterator25 = _createForOfIteratorHelperLoose(chain.features), _step25; !(_step25 = _iterator25()).done;) {
        var feature = _step25.value;
        var f = void 0;
        if (f = features[feature.featureType]) {
          if (f[feature.featureSetting]) {
            flags &= feature.disableFlags;
            flags |= feature.enableFlags;
          } else if (f[feature.featureSetting] === false) {
            flags |= ~feature.disableFlags;
            flags &= ~feature.enableFlags;
          }
        }
      }
      for (var _iterator26 = _createForOfIteratorHelperLoose(chain.subtables), _step26; !(_step26 = _iterator26()).done;) {
        var subtable = _step26.value;
        if (subtable.subFeatureFlags & flags) {
          this.processSubtable(subtable, glyphs);
        }
      }
    } // remove deleted glyphs

    var index = glyphs.length - 1;
    while (index >= 0) {
      if (glyphs[index].id === 0xffff) {
        glyphs.splice(index, 1);
      }
      index--;
    }
    return glyphs;
  };
  _proto21.processSubtable = function processSubtable(subtable, glyphs) {
    this.subtable = subtable;
    this.glyphs = glyphs;
    if (this.subtable.type === 4) {
      this.processNoncontextualSubstitutions(this.subtable, this.glyphs);
      return;
    }
    this.ligatureStack = [];
    this.markedGlyph = null;
    this.firstGlyph = null;
    this.lastGlyph = null;
    this.markedIndex = null;
    var stateMachine = this.getStateMachine(subtable);
    var process = this.getProcessor();
    var reverse = !!(this.subtable.coverage & REVERSE_DIRECTION);
    return stateMachine.process(this.glyphs, reverse, process);
  };
  _proto21.getStateMachine = function getStateMachine(subtable) {
    return new AATStateMachine(subtable.table.stateTable);
  };
  _proto21.getProcessor = function getProcessor() {
    switch (this.subtable.type) {
      case 0:
        return this.processIndicRearragement;
      case 1:
        return this.processContextualSubstitution;
      case 2:
        return this.processLigature;
      case 4:
        return this.processNoncontextualSubstitutions;
      case 5:
        return this.processGlyphInsertion;
      default:
        throw new Error("Invalid morx subtable type: ".concat(this.subtable.type));
    }
  };
  _proto21.processIndicRearragement = function processIndicRearragement(glyph, entry, index) {
    if (entry.flags & MARK_FIRST) {
      this.firstGlyph = index;
    }
    if (entry.flags & MARK_LAST) {
      this.lastGlyph = index;
    }
    reorderGlyphs(this.glyphs, entry.flags & VERB, this.firstGlyph, this.lastGlyph);
  };
  _proto21.processContextualSubstitution = function processContextualSubstitution(glyph, entry, index) {
    var subsitutions = this.subtable.table.substitutionTable.items;
    if (entry.markIndex !== 0xffff) {
      var lookup = subsitutions.getItem(entry.markIndex);
      var lookupTable = new AATLookupTable(lookup);
      glyph = this.glyphs[this.markedGlyph];
      var gid = lookupTable.lookup(glyph.id);
      if (gid) {
        this.glyphs[this.markedGlyph] = this.font.getGlyph(gid, glyph.codePoints);
      }
    }
    if (entry.currentIndex !== 0xffff) {
      var _lookup = subsitutions.getItem(entry.currentIndex);
      var _lookupTable = new AATLookupTable(_lookup);
      glyph = this.glyphs[index];
      var gid = _lookupTable.lookup(glyph.id);
      if (gid) {
        this.glyphs[index] = this.font.getGlyph(gid, glyph.codePoints);
      }
    }
    if (entry.flags & SET_MARK) {
      this.markedGlyph = index;
    }
  };
  _proto21.processLigature = function processLigature(glyph, entry, index) {
    if (entry.flags & SET_COMPONENT) {
      this.ligatureStack.push(index);
    }
    if (entry.flags & PERFORM_ACTION) {
      var _this$ligatureStack;
      var actions = this.subtable.table.ligatureActions;
      var components = this.subtable.table.components;
      var ligatureList = this.subtable.table.ligatureList;
      var actionIndex = entry.action;
      var last = false;
      var ligatureIndex = 0;
      var codePoints = [];
      var ligatureGlyphs = [];
      while (!last) {
        var _codePoints;
        var componentGlyph = this.ligatureStack.pop();
        (_codePoints = codePoints).unshift.apply(_codePoints, this.glyphs[componentGlyph].codePoints);
        var action = actions.getItem(actionIndex++);
        last = !!(action & LAST_MASK);
        var store = !!(action & STORE_MASK);
        var offset = (action & OFFSET_MASK) << 2 >> 2; // sign extend 30 to 32 bits

        offset += this.glyphs[componentGlyph].id;
        var component = components.getItem(offset);
        ligatureIndex += component;
        if (last || store) {
          var ligatureEntry = ligatureList.getItem(ligatureIndex);
          this.glyphs[componentGlyph] = this.font.getGlyph(ligatureEntry, codePoints);
          ligatureGlyphs.push(componentGlyph);
          ligatureIndex = 0;
          codePoints = [];
        } else {
          this.glyphs[componentGlyph] = this.font.getGlyph(0xffff);
        }
      } // Put ligature glyph indexes back on the stack

      (_this$ligatureStack = this.ligatureStack).push.apply(_this$ligatureStack, ligatureGlyphs);
    }
  };
  _proto21.processNoncontextualSubstitutions = function processNoncontextualSubstitutions(subtable, glyphs, index) {
    var lookupTable = new AATLookupTable(subtable.table.lookupTable);
    for (index = 0; index < glyphs.length; index++) {
      var glyph = glyphs[index];
      if (glyph.id !== 0xffff) {
        var gid = lookupTable.lookup(glyph.id);
        if (gid) {
          // 0 means do nothing
          glyphs[index] = this.font.getGlyph(gid, glyph.codePoints);
        }
      }
    }
  };
  _proto21._insertGlyphs = function _insertGlyphs(glyphIndex, insertionActionIndex, count, isBefore) {
    var _this$glyphs;
    var insertions = [];
    while (count--) {
      var gid = this.subtable.table.insertionActions.getItem(insertionActionIndex++);
      insertions.push(this.font.getGlyph(gid));
    }
    if (!isBefore) {
      glyphIndex++;
    }
    (_this$glyphs = this.glyphs).splice.apply(_this$glyphs, [glyphIndex, 0].concat(insertions));
  };
  _proto21.processGlyphInsertion = function processGlyphInsertion(glyph, entry, index) {
    if (entry.flags & SET_MARK) {
      this.markedIndex = index;
    }
    if (entry.markedInsertIndex !== 0xffff) {
      var count = (entry.flags & MARKED_INSERT_COUNT) >>> 5;
      var isBefore = !!(entry.flags & MARKED_INSERT_BEFORE);
      this._insertGlyphs(this.markedIndex, entry.markedInsertIndex, count, isBefore);
    }
    if (entry.currentInsertIndex !== 0xffff) {
      var _count = (entry.flags & CURRENT_INSERT_COUNT) >>> 5;
      var _isBefore = !!(entry.flags & CURRENT_INSERT_BEFORE);
      this._insertGlyphs(index, entry.currentInsertIndex, _count, _isBefore);
    }
  };
  _proto21.getSupportedFeatures = function getSupportedFeatures() {
    var features = [];
    for (var _iterator27 = _createForOfIteratorHelperLoose(this.morx.chains), _step27; !(_step27 = _iterator27()).done;) {
      var chain = _step27.value;
      for (var _iterator28 = _createForOfIteratorHelperLoose(chain.features), _step28; !(_step28 = _iterator28()).done;) {
        var feature = _step28.value;
        features.push([feature.featureType, feature.featureSetting]);
      }
    }
    return features;
  };
  _proto21.generateInputs = function generateInputs(gid) {
    if (!this.inputCache) {
      this.generateInputCache();
    }
    return this.inputCache[gid] || [];
  };
  _proto21.generateInputCache = function generateInputCache() {
    this.inputCache = {};
    for (var _iterator29 = _createForOfIteratorHelperLoose(this.morx.chains), _step29; !(_step29 = _iterator29()).done;) {
      var chain = _step29.value;
      var flags = chain.defaultFlags;
      for (var _iterator30 = _createForOfIteratorHelperLoose(chain.subtables), _step30; !(_step30 = _iterator30()).done;) {
        var subtable = _step30.value;
        if (subtable.subFeatureFlags & flags) {
          this.generateInputsForSubtable(subtable);
        }
      }
    }
  };
  _proto21.generateInputsForSubtable = function generateInputsForSubtable(subtable) {
    var _this3 = this;
    // Currently, only supporting ligature subtables.
    if (subtable.type !== 2) {
      return;
    }
    var reverse = !!(subtable.coverage & REVERSE_DIRECTION);
    if (reverse) {
      throw new Error('Reverse subtable, not supported.');
    }
    this.subtable = subtable;
    this.ligatureStack = [];
    var stateMachine = this.getStateMachine(subtable);
    var process = this.getProcessor();
    var input = [];
    var stack = [];
    this.glyphs = [];
    stateMachine.traverse({
      enter: function enter(glyph, entry) {
        var glyphs = _this3.glyphs;
        stack.push({
          glyphs: glyphs.slice(),
          ligatureStack: _this3.ligatureStack.slice()
        }); // Add glyph to input and glyphs to process.

        var g = _this3.font.getGlyph(glyph);
        input.push(g);
        glyphs.push(input[input.length - 1]); // Process ligature substitution

        process(glyphs[glyphs.length - 1], entry, glyphs.length - 1); // Add input to result if only one matching (non-deleted) glyph remains.

        var count = 0;
        var found = 0;
        for (var i = 0; i < glyphs.length && count <= 1; i++) {
          if (glyphs[i].id !== 0xffff) {
            count++;
            found = glyphs[i].id;
          }
        }
        if (count === 1) {
          var result = input.map(function (g) {
            return g.id;
          });
          var _cache = _this3.inputCache[found];
          if (_cache) {
            _cache.push(result);
          } else {
            _this3.inputCache[found] = [result];
          }
        }
      },
      exit: function exit() {
        var _stack$pop = stack.pop();
        _this3.glyphs = _stack$pop.glyphs;
        _this3.ligatureStack = _stack$pop.ligatureStack;
        input.pop();
      }
    });
  };
  return AATMorxProcessor;
}(), _applyDecoratedDescriptor(_class$2.prototype, "getStateMachine", [cache], Object.getOwnPropertyDescriptor(_class$2.prototype, "getStateMachine"), _class$2.prototype), _class$2);
// reverse the glyphs inside those ranges if specified
// ranges are in [offset, length] format

function swap(glyphs, rangeA, rangeB, reverseA, reverseB) {
  if (reverseA === void 0) {
    reverseA = false;
  }
  if (reverseB === void 0) {
    reverseB = false;
  }
  var end = glyphs.splice(rangeB[0] - (rangeB[1] - 1), rangeB[1]);
  if (reverseB) {
    end.reverse();
  }
  var start = glyphs.splice.apply(glyphs, [rangeA[0], rangeA[1]].concat(end));
  if (reverseA) {
    start.reverse();
  }
  glyphs.splice.apply(glyphs, [rangeB[0] - (rangeA[1] - 1), 0].concat(start));
  return glyphs;
}
function reorderGlyphs(glyphs, verb, firstGlyph, lastGlyph) {
  switch (verb) {
    case 0:
      // no change
      return glyphs;
    case 1:
      // Ax => xA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 0]);
    case 2:
      // xD => Dx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 1]);
    case 3:
      // AxD => DxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 1]);
    case 4:
      // ABx => xAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 0]);
    case 5:
      // ABx => xBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 0], true, false);
    case 6:
      // xCD => CDx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 2]);
    case 7:
      // xCD => DCx
      return swap(glyphs, [firstGlyph, 0], [lastGlyph, 2], false, true);
    case 8:
      // AxCD => CDxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 2]);
    case 9:
      // AxCD => DCxA
      return swap(glyphs, [firstGlyph, 1], [lastGlyph, 2], false, true);
    case 10:
      // ABxD => DxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 1]);
    case 11:
      // ABxD => DxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 1], true, false);
    case 12:
      // ABxCD => CDxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2]);
    case 13:
      // ABxCD => CDxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], true, false);
    case 14:
      // ABxCD => DCxAB
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], false, true);
    case 15:
      // ABxCD => DCxBA
      return swap(glyphs, [firstGlyph, 2], [lastGlyph, 2], true, true);
    default:
      throw new Error("Unknown verb: ".concat(verb));
  }
}
var AATLayoutEngine = /*#__PURE__*/function () {
  function AATLayoutEngine(font) {
    this.font = font;
    this.morxProcessor = new AATMorxProcessor(font);
    this.fallbackPosition = false;
  }
  var _proto22 = AATLayoutEngine.prototype;
  _proto22.substitute = function substitute(glyphRun) {
    // AAT expects the glyphs to be in visual order prior to morx processing,
    // so reverse the glyphs if the script is right-to-left.
    if (glyphRun.direction === 'rtl') {
      glyphRun.glyphs.reverse();
    }
    this.morxProcessor.process(glyphRun.glyphs, mapOTToAAT(glyphRun.features));
  };
  _proto22.getAvailableFeatures = function getAvailableFeatures(script, language) {
    return mapAATToOT(this.morxProcessor.getSupportedFeatures());
  };
  _proto22.stringsForGlyph = function stringsForGlyph(gid) {
    var glyphStrings = this.morxProcessor.generateInputs(gid);
    var result = new Set();
    for (var _iterator31 = _createForOfIteratorHelperLoose(glyphStrings), _step31; !(_step31 = _iterator31()).done;) {
      var glyphs = _step31.value;
      this._addStrings(glyphs, 0, result, '');
    }
    return result;
  };
  _proto22._addStrings = function _addStrings(glyphs, index, strings, string) {
    var codePoints = this.font._cmapProcessor.codePointsForGlyph(glyphs[index]);
    for (var _iterator32 = _createForOfIteratorHelperLoose(codePoints), _step32; !(_step32 = _iterator32()).done;) {
      var codePoint = _step32.value;
      var s = string + String.fromCodePoint(codePoint);
      if (index < glyphs.length - 1) {
        this._addStrings(glyphs, index + 1, strings, s);
      } else {
        strings.add(s);
      }
    }
  };
  return AATLayoutEngine;
}();
/**
 * ShapingPlans are used by the OpenType shapers to store which
 * features should by applied, and in what order to apply them.
 * The features are applied in groups called stages. A feature
 * can be applied globally to all glyphs, or locally to only
 * specific glyphs.
 *
 * @private
 */
var ShapingPlan = /*#__PURE__*/function () {
  function ShapingPlan(font, script, direction) {
    this.font = font;
    this.script = script;
    this.direction = direction;
    this.stages = [];
    this.globalFeatures = {};
    this.allFeatures = {};
  }
  /**
   * Adds the given features to the last stage.
   * Ignores features that have already been applied.
   */
  var _proto23 = ShapingPlan.prototype;
  _proto23._addFeatures = function _addFeatures(features, global) {
    var stageIndex = this.stages.length - 1;
    var stage = this.stages[stageIndex];
    for (var _iterator33 = _createForOfIteratorHelperLoose(features), _step33; !(_step33 = _iterator33()).done;) {
      var feature = _step33.value;
      if (this.allFeatures[feature] == null) {
        stage.push(feature);
        this.allFeatures[feature] = stageIndex;
        if (global) {
          this.globalFeatures[feature] = true;
        }
      }
    }
  }
  /**
   * Add features to the last stage
   */;
  _proto23.add = function add(arg, global) {
    if (global === void 0) {
      global = true;
    }
    if (this.stages.length === 0) {
      this.stages.push([]);
    }
    if (typeof arg === 'string') {
      arg = [arg];
    }
    if (Array.isArray(arg)) {
      this._addFeatures(arg, global);
    } else if (typeof arg === 'object') {
      this._addFeatures(arg.global || [], true);
      this._addFeatures(arg.local || [], false);
    } else {
      throw new Error("Unsupported argument to ShapingPlan#add");
    }
  }
  /**
   * Add a new stage
   */;
  _proto23.addStage = function addStage(arg, global) {
    if (typeof arg === 'function') {
      this.stages.push(arg, []);
    } else {
      this.stages.push([]);
      this.add(arg, global);
    }
  };
  _proto23.setFeatureOverrides = function setFeatureOverrides(features) {
    if (Array.isArray(features)) {
      this.add(features);
    } else if (typeof features === 'object') {
      for (var tag in features) {
        if (features[tag]) {
          this.add(tag);
        } else if (this.allFeatures[tag] != null) {
          var stage = this.stages[this.allFeatures[tag]];
          stage.splice(stage.indexOf(tag), 1);
          delete this.allFeatures[tag];
          delete this.globalFeatures[tag];
        }
      }
    }
  }
  /**
   * Assigns the global features to the given glyphs
   */;
  _proto23.assignGlobalFeatures = function assignGlobalFeatures(glyphs) {
    for (var _iterator34 = _createForOfIteratorHelperLoose(glyphs), _step34; !(_step34 = _iterator34()).done;) {
      var glyph = _step34.value;
      for (var feature in this.globalFeatures) {
        glyph.features[feature] = true;
      }
    }
  }
  /**
   * Executes the planned stages using the given OTProcessor
   */;
  _proto23.process = function process(processor, glyphs, positions) {
    for (var _iterator35 = _createForOfIteratorHelperLoose(this.stages), _step35; !(_step35 = _iterator35()).done;) {
      var stage = _step35.value;
      if (typeof stage === 'function') {
        if (!positions) {
          stage(this.font, glyphs, this);
        }
      } else if (stage.length > 0) {
        processor.applyFeatures(stage, glyphs, positions);
      }
    }
  };
  return ShapingPlan;
}();
var VARIATION_FEATURES = ['rvrn'];
var COMMON_FEATURES = ['ccmp', 'locl', 'rlig', 'mark', 'mkmk'];
var FRACTIONAL_FEATURES = ['frac', 'numr', 'dnom'];
var HORIZONTAL_FEATURES = ['calt', 'clig', 'liga', 'rclt', 'curs', 'kern'];
var DIRECTIONAL_FEATURES = {
  ltr: ['ltra', 'ltrm'],
  rtl: ['rtla', 'rtlm']
};
var DefaultShaper = /*#__PURE__*/function () {
  function DefaultShaper() {}
  DefaultShaper.plan = function plan(_plan, glyphs, features) {
    // Plan the features we want to apply
    this.planPreprocessing(_plan);
    this.planFeatures(_plan);
    this.planPostprocessing(_plan, features); // Assign the global features to all the glyphs

    _plan.assignGlobalFeatures(glyphs); // Assign local features to glyphs

    this.assignFeatures(_plan, glyphs);
  };
  DefaultShaper.planPreprocessing = function planPreprocessing(plan) {
    plan.add({
      global: [].concat(VARIATION_FEATURES, DIRECTIONAL_FEATURES[plan.direction]),
      local: FRACTIONAL_FEATURES
    });
  };
  DefaultShaper.planFeatures = function planFeatures(plan) {// Do nothing by default. Let subclasses override this.
  };
  DefaultShaper.planPostprocessing = function planPostprocessing(plan, userFeatures) {
    plan.add([].concat(COMMON_FEATURES, HORIZONTAL_FEATURES));
    plan.setFeatureOverrides(userFeatures);
  };
  DefaultShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    // Enable contextual fractions
    for (var i = 0; i < glyphs.length; i++) {
      var glyph = glyphs[i];
      if (glyph.codePoints[0] === 0x2044) {
        // fraction slash
        var start = i;
        var end = i + 1; // Apply numerator

        while (start > 0 && unicode.isDigit(glyphs[start - 1].codePoints[0])) {
          glyphs[start - 1].features.numr = true;
          glyphs[start - 1].features.frac = true;
          start--;
        } // Apply denominator

        while (end < glyphs.length && unicode.isDigit(glyphs[end].codePoints[0])) {
          glyphs[end].features.dnom = true;
          glyphs[end].features.frac = true;
          end++;
        } // Apply fraction slash

        glyph.features.frac = true;
        i = end - 1;
      }
    }
  };
  return DefaultShaper;
}();
_defineProperty(DefaultShaper, "zeroMarkWidths", 'AFTER_GPOS');
var trie = new UnicodeTrie(Buffer("APABAAAAAAAAOAAAAf0BAv7tmi1MxDAUx7vtvjhAgcDgkEgEAnmXEBIMCYaEcygEiqBQ4FAkCE4ikUgMiiBJSAgSiUQSDMn9L9eSl6bddddug9t7yS/trevre+3r27pcNxZiG+yCfdCVv/9LeQxOwRm4AJegD27ALbgD9+ABPJF+z+BN/h7yDj5k/VOWX6SdmU5+wLWknggxDxaS8u0qiiX4uiz9XamQ3wzDMAzDMAzDMAzDVI/h959V/v7BMAzDMAzDMLlyNTNiMSdewVxbiA44B4/guz1qW58VYlMI0WsJ0W+N6kXw0spvPtdwhtkwnGM6uLaV4Xyzg3v3PM9DPfQ/sOg4xPWjipy31P8LTqbU304c/cLCUmWJLNB2Uz2U1KTeRKNmKHVMfbJC+/0loTZRH/W5cvEvBJPMbREkWt3FD1NcqXZBSpuE2Ad0PBehPtNrPtIEdYP+hiRt/V1jIiE69X4NT/uVZI3PUHE9bm5M7ePGdZWy951v7Nn6j8v1WWKP3mt6ttnsigx6VN7Vc0VomSSGqW2mGNP1muZPl7LfjNUaKNFtDGVf2fvE9O7VlBS5j333c5p/eeoOqcs1R/hIqDWLJ7TTlksirVT1SI7l8k4Yp+g3jafGcrU1RM6l9th80XOpnlN97bDNY4i4s61B0Si/ipa0uHMl6zqEjlFfCZm/TM8KmzQDjmuTAQ==","base64"));
var FEATURES = ['isol', 'fina', 'fin2', 'fin3', 'medi', 'med2', 'init'];
var ShapingClasses = {
  Non_Joining: 0,
  Left_Joining: 1,
  Right_Joining: 2,
  Dual_Joining: 3,
  Join_Causing: 3,
  ALAPH: 4,
  'DALATH RISH': 5,
  Transparent: 6
};
var ISOL = 'isol';
var FINA = 'fina';
var FIN2 = 'fin2';
var FIN3 = 'fin3';
var MEDI = 'medi';
var MED2 = 'med2';
var INIT = 'init';
var NONE = null; // Each entry is [prevAction, curAction, nextState]

var STATE_TABLE = [
//   Non_Joining,        Left_Joining,       Right_Joining,     Dual_Joining,           ALAPH,            DALATH RISH
// State 0: prev was U,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 6]],
// State 1: prev was R or ISOL/ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, FIN2, 5], [NONE, ISOL, 6]],
// State 2: prev was D/L in ISOL form,  willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [INIT, FINA, 1], [INIT, FINA, 3], [INIT, FINA, 4], [INIT, FINA, 6]],
// State 3: prev was D in FINA form,  willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [MEDI, FINA, 1], [MEDI, FINA, 3], [MEDI, FINA, 4], [MEDI, FINA, 6]],
// State 4: prev was FINA ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [MED2, ISOL, 1], [MED2, ISOL, 2], [MED2, FIN2, 5], [MED2, ISOL, 6]],
// State 5: prev was FIN2/FIN3 ALAPH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [ISOL, ISOL, 1], [ISOL, ISOL, 2], [ISOL, FIN2, 5], [ISOL, ISOL, 6]],
// State 6: prev was DALATH/RISH,  not willing to join.
[[NONE, NONE, 0], [NONE, ISOL, 2], [NONE, ISOL, 1], [NONE, ISOL, 2], [NONE, FIN3, 5], [NONE, ISOL, 6]]];
/**
 * This is a shaper for Arabic, and other cursive scripts.
 * It uses data from ArabicShaping.txt in the Unicode database,
 * compiled to a UnicodeTrie by generate-data.coffee.
 *
 * The shaping state machine was ported from Harfbuzz.
 * https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-complex-arabic.cc
 */
var ArabicShaper = /*#__PURE__*/function (_DefaultShaper) {
  function ArabicShaper() {
    return _DefaultShaper.apply(this, arguments) || this;
  }
  _inheritsLoose(ArabicShaper, _DefaultShaper);
  ArabicShaper.planFeatures = function planFeatures(plan) {
    plan.add(['ccmp', 'locl']);
    for (var i = 0; i < FEATURES.length; i++) {
      var feature = FEATURES[i];
      plan.addStage(feature, false);
    }
    plan.addStage('mset');
  };
  ArabicShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    _DefaultShaper.assignFeatures.call(this, plan, glyphs);
    var prev = -1;
    var state = 0;
    var actions = []; // Apply the state machine to map glyphs to features

    for (var i = 0; i < glyphs.length; i++) {
      var curAction = void 0,
        prevAction = void 0;
      var glyph = glyphs[i];
      var type = getShapingClass(glyph.codePoints[0]);
      if (type === ShapingClasses.Transparent) {
        actions[i] = NONE;
        continue;
      }
      var _STATE_TABLE$state$ty = STATE_TABLE[state][type];
      prevAction = _STATE_TABLE$state$ty[0];
      curAction = _STATE_TABLE$state$ty[1];
      state = _STATE_TABLE$state$ty[2];
      if (prevAction !== NONE && prev !== -1) {
        actions[prev] = prevAction;
      }
      actions[i] = curAction;
      prev = i;
    } // Apply the chosen features to their respective glyphs

    for (var index = 0; index < glyphs.length; index++) {
      var feature = void 0;
      var glyph = glyphs[index];
      if (feature = actions[index]) {
        glyph.features[feature] = true;
      }
    }
  };
  return ArabicShaper;
}(DefaultShaper);
function getShapingClass(codePoint) {
  var res = trie.get(codePoint);
  if (res) {
    return res - 1;
  }
  var category = unicode.getCategory(codePoint);
  if (category === 'Mn' || category === 'Me' || category === 'Cf') {
    return ShapingClasses.Transparent;
  }
  return ShapingClasses.Non_Joining;
}
var GlyphIterator = /*#__PURE__*/function () {
  function GlyphIterator(glyphs, options) {
    this.glyphs = glyphs;
    this.reset(options);
  }
  var _proto24 = GlyphIterator.prototype;
  _proto24.reset = function reset(options, index) {
    if (options === void 0) {
      options = {};
    }
    if (index === void 0) {
      index = 0;
    }
    this.options = options;
    this.flags = options.flags || {};
    this.markAttachmentType = options.markAttachmentType || 0;
    this.index = index;
  };
  _proto24.shouldIgnore = function shouldIgnore(glyph) {
    return this.flags.ignoreMarks && glyph.isMark || this.flags.ignoreBaseGlyphs && glyph.isBase || this.flags.ignoreLigatures && glyph.isLigature || this.markAttachmentType && glyph.isMark && glyph.markAttachmentType !== this.markAttachmentType;
  };
  _proto24.move = function move(dir) {
    this.index += dir;
    while (0 <= this.index && this.index < this.glyphs.length && this.shouldIgnore(this.glyphs[this.index])) {
      this.index += dir;
    }
    if (0 > this.index || this.index >= this.glyphs.length) {
      return null;
    }
    return this.glyphs[this.index];
  };
  _proto24.next = function next() {
    return this.move(+1);
  };
  _proto24.prev = function prev() {
    return this.move(-1);
  };
  _proto24.peek = function peek(count) {
    if (count === void 0) {
      count = 1;
    }
    var idx = this.index;
    var res = this.increment(count);
    this.index = idx;
    return res;
  };
  _proto24.peekIndex = function peekIndex(count) {
    if (count === void 0) {
      count = 1;
    }
    var idx = this.index;
    this.increment(count);
    var res = this.index;
    this.index = idx;
    return res;
  };
  _proto24.increment = function increment(count) {
    if (count === void 0) {
      count = 1;
    }
    var dir = count < 0 ? -1 : 1;
    count = Math.abs(count);
    while (count--) {
      this.move(dir);
    }
    return this.glyphs[this.index];
  };
  return _createClass(GlyphIterator, [{
    key: "cur",
    get: function get() {
      return this.glyphs[this.index] || null;
    }
  }]);
}();
var DEFAULT_SCRIPTS = ['DFLT', 'dflt', 'latn'];
var OTProcessor = /*#__PURE__*/function () {
  function OTProcessor(font, table) {
    this.font = font;
    this.table = table;
    this.script = null;
    this.scriptTag = null;
    this.language = null;
    this.languageTag = null;
    this.features = {};
    this.lookups = {}; // Setup variation substitutions

    this.variationsIndex = font._variationProcessor ? this.findVariationsIndex(font._variationProcessor.normalizedCoords) : -1; // initialize to default script + language

    this.selectScript(); // current context (set by applyFeatures)

    this.glyphs = [];
    this.positions = []; // only used by GPOS

    this.ligatureID = 1;
    this.currentFeature = null;
  }
  var _proto25 = OTProcessor.prototype;
  _proto25.findScript = function findScript(script) {
    if (this.table.scriptList == null) {
      return null;
    }
    if (!Array.isArray(script)) {
      script = [script];
    }
    for (var _iterator36 = _createForOfIteratorHelperLoose(script), _step36; !(_step36 = _iterator36()).done;) {
      var s = _step36.value;
      for (var _iterator37 = _createForOfIteratorHelperLoose(this.table.scriptList), _step37; !(_step37 = _iterator37()).done;) {
        var entry = _step37.value;
        if (entry.tag === s) {
          return entry;
        }
      }
    }
    return null;
  };
  _proto25.selectScript = function selectScript(script, language, direction$1) {
    var changed = false;
    var entry;
    if (!this.script || script !== this.scriptTag) {
      entry = this.findScript(script);
      if (!entry) {
        entry = this.findScript(DEFAULT_SCRIPTS);
      }
      if (!entry) {
        return this.scriptTag;
      }
      this.scriptTag = entry.tag;
      this.script = entry.script;
      this.language = null;
      this.languageTag = null;
      changed = true;
    }
    if (!direction$1 || direction$1 !== this.direction) {
      this.direction = direction$1 || direction(script);
    }
    if (language && language.length < 4) {
      language += ' '.repeat(4 - language.length);
    }
    if (!language || language !== this.languageTag) {
      this.language = null;
      for (var _iterator38 = _createForOfIteratorHelperLoose(this.script.langSysRecords), _step38; !(_step38 = _iterator38()).done;) {
        var lang = _step38.value;
        if (lang.tag === language) {
          this.language = lang.langSys;
          this.languageTag = lang.tag;
          break;
        }
      }
      if (!this.language) {
        this.language = this.script.defaultLangSys;
        this.languageTag = null;
      }
      changed = true;
    } // Build a feature lookup table

    if (changed) {
      this.features = {};
      if (this.language) {
        for (var _iterator39 = _createForOfIteratorHelperLoose(this.language.featureIndexes), _step39; !(_step39 = _iterator39()).done;) {
          var featureIndex = _step39.value;
          var record = this.table.featureList[featureIndex];
          var substituteFeature = this.substituteFeatureForVariations(featureIndex);
          this.features[record.tag] = substituteFeature || record.feature;
        }
      }
    }
    return this.scriptTag;
  };
  _proto25.lookupsForFeatures = function lookupsForFeatures(userFeatures, exclude) {
    if (userFeatures === void 0) {
      userFeatures = [];
    }
    var lookups = [];
    for (var _iterator40 = _createForOfIteratorHelperLoose(userFeatures), _step40; !(_step40 = _iterator40()).done;) {
      var tag = _step40.value;
      var feature = this.features[tag];
      if (!feature) {
        continue;
      }
      for (var _iterator41 = _createForOfIteratorHelperLoose(feature.lookupListIndexes), _step41; !(_step41 = _iterator41()).done;) {
        var lookupIndex = _step41.value;
        if (exclude && exclude.indexOf(lookupIndex) !== -1) {
          continue;
        }
        lookups.push({
          feature: tag,
          index: lookupIndex,
          lookup: this.table.lookupList.get(lookupIndex)
        });
      }
    }
    lookups.sort(function (a, b) {
      return a.index - b.index;
    });
    return lookups;
  };
  _proto25.substituteFeatureForVariations = function substituteFeatureForVariations(featureIndex) {
    if (this.variationsIndex === -1) {
      return null;
    }
    var record = this.table.featureVariations.featureVariationRecords[this.variationsIndex];
    var substitutions = record.featureTableSubstitution.substitutions;
    for (var _iterator42 = _createForOfIteratorHelperLoose(substitutions), _step42; !(_step42 = _iterator42()).done;) {
      var substitution = _step42.value;
      if (substitution.featureIndex === featureIndex) {
        return substitution.alternateFeatureTable;
      }
    }
    return null;
  };
  _proto25.findVariationsIndex = function findVariationsIndex(coords) {
    var variations = this.table.featureVariations;
    if (!variations) {
      return -1;
    }
    var records = variations.featureVariationRecords;
    for (var i = 0; i < records.length; i++) {
      var conditions = records[i].conditionSet.conditionTable;
      if (this.variationConditionsMatch(conditions, coords)) {
        return i;
      }
    }
    return -1;
  };
  _proto25.variationConditionsMatch = function variationConditionsMatch(conditions, coords) {
    return conditions.every(function (condition) {
      var coord = condition.axisIndex < coords.length ? coords[condition.axisIndex] : 0;
      return condition.filterRangeMinValue <= coord && coord <= condition.filterRangeMaxValue;
    });
  };
  _proto25.applyFeatures = function applyFeatures(userFeatures, glyphs, advances) {
    var lookups = this.lookupsForFeatures(userFeatures);
    this.applyLookups(lookups, glyphs, advances);
  };
  _proto25.applyLookups = function applyLookups(lookups, glyphs, positions) {
    this.glyphs = glyphs;
    this.positions = positions;
    this.glyphIterator = new GlyphIterator(glyphs);
    for (var _iterator43 = _createForOfIteratorHelperLoose(lookups), _step43; !(_step43 = _iterator43()).done;) {
      var _step43$value = _step43.value,
        feature = _step43$value.feature,
        lookup = _step43$value.lookup;
      this.currentFeature = feature;
      this.glyphIterator.reset(lookup.flags);
      while (this.glyphIterator.index < glyphs.length) {
        if (!(feature in this.glyphIterator.cur.features)) {
          this.glyphIterator.next();
          continue;
        }
        for (var _iterator44 = _createForOfIteratorHelperLoose(lookup.subTables), _step44; !(_step44 = _iterator44()).done;) {
          var table = _step44.value;
          var res = this.applyLookup(lookup.lookupType, table);
          if (res) {
            break;
          }
        }
        this.glyphIterator.next();
      }
    }
  };
  _proto25.applyLookup = function applyLookup(lookup, table) {
    throw new Error("applyLookup must be implemented by subclasses");
  };
  _proto25.applyLookupList = function applyLookupList(lookupRecords) {
    var options = this.glyphIterator.options;
    var glyphIndex = this.glyphIterator.index;
    for (var _iterator45 = _createForOfIteratorHelperLoose(lookupRecords), _step45; !(_step45 = _iterator45()).done;) {
      var lookupRecord = _step45.value;
      // Reset flags and find glyph index for this lookup record
      this.glyphIterator.reset(options, glyphIndex);
      this.glyphIterator.increment(lookupRecord.sequenceIndex); // Get the lookup and setup flags for subtables

      var lookup = this.table.lookupList.get(lookupRecord.lookupListIndex);
      this.glyphIterator.reset(lookup.flags, this.glyphIterator.index); // Apply lookup subtables until one matches

      for (var _iterator46 = _createForOfIteratorHelperLoose(lookup.subTables), _step46; !(_step46 = _iterator46()).done;) {
        var table = _step46.value;
        if (this.applyLookup(lookup.lookupType, table)) {
          break;
        }
      }
    }
    this.glyphIterator.reset(options, glyphIndex);
    return true;
  };
  _proto25.coverageIndex = function coverageIndex(coverage, glyph) {
    if (glyph == null) {
      glyph = this.glyphIterator.cur.id;
    }
    switch (coverage.version) {
      case 1:
        return coverage.glyphs.indexOf(glyph);
      case 2:
        for (var _iterator47 = _createForOfIteratorHelperLoose(coverage.rangeRecords), _step47; !(_step47 = _iterator47()).done;) {
          var range = _step47.value;
          if (range.start <= glyph && glyph <= range.end) {
            return range.startCoverageIndex + glyph - range.start;
          }
        }
        break;
    }
    return -1;
  };
  _proto25.match = function match(sequenceIndex, sequence, fn, matched) {
    var pos = this.glyphIterator.index;
    var glyph = this.glyphIterator.increment(sequenceIndex);
    var idx = 0;
    while (idx < sequence.length && glyph && fn(sequence[idx], glyph)) {
      if (matched) {
        matched.push(this.glyphIterator.index);
      }
      idx++;
      glyph = this.glyphIterator.next();
    }
    this.glyphIterator.index = pos;
    if (idx < sequence.length) {
      return false;
    }
    return matched || true;
  };
  _proto25.sequenceMatches = function sequenceMatches(sequenceIndex, sequence) {
    return this.match(sequenceIndex, sequence, function (component, glyph) {
      return component === glyph.id;
    });
  };
  _proto25.sequenceMatchIndices = function sequenceMatchIndices(sequenceIndex, sequence) {
    var _this4 = this;
    return this.match(sequenceIndex, sequence, function (component, glyph) {
      // If the current feature doesn't apply to this glyph,
      if (!(_this4.currentFeature in glyph.features)) {
        return false;
      }
      return component === glyph.id;
    }, []);
  };
  _proto25.coverageSequenceMatches = function coverageSequenceMatches(sequenceIndex, sequence) {
    var _this5 = this;
    return this.match(sequenceIndex, sequence, function (coverage, glyph) {
      return _this5.coverageIndex(coverage, glyph.id) >= 0;
    });
  };
  _proto25.getClassID = function getClassID(glyph, classDef) {
    switch (classDef.version) {
      case 1:
        // Class array
        var i = glyph - classDef.startGlyph;
        if (i >= 0 && i < classDef.classValueArray.length) {
          return classDef.classValueArray[i];
        }
        break;
      case 2:
        for (var _iterator48 = _createForOfIteratorHelperLoose(classDef.classRangeRecord), _step48; !(_step48 = _iterator48()).done;) {
          var range = _step48.value;
          if (range.start <= glyph && glyph <= range.end) {
            return range.class;
          }
        }
        break;
    }
    return 0;
  };
  _proto25.classSequenceMatches = function classSequenceMatches(sequenceIndex, sequence, classDef) {
    var _this6 = this;
    return this.match(sequenceIndex, sequence, function (classID, glyph) {
      return classID === _this6.getClassID(glyph.id, classDef);
    });
  };
  _proto25.applyContext = function applyContext(table) {
    switch (table.version) {
      case 1:
        var index = this.coverageIndex(table.coverage);
        if (index === -1) {
          return false;
        }
        var set = table.ruleSets[index];
        for (var _iterator49 = _createForOfIteratorHelperLoose(set), _step49; !(_step49 = _iterator49()).done;) {
          var rule = _step49.value;
          if (this.sequenceMatches(1, rule.input)) {
            return this.applyLookupList(rule.lookupRecords);
          }
        }
        break;
      case 2:
        if (this.coverageIndex(table.coverage) === -1) {
          return false;
        }
        index = this.getClassID(this.glyphIterator.cur.id, table.classDef);
        if (index === -1) {
          return false;
        }
        set = table.classSet[index];
        for (var _iterator50 = _createForOfIteratorHelperLoose(set), _step50; !(_step50 = _iterator50()).done;) {
          var _rule = _step50.value;
          if (this.classSequenceMatches(1, _rule.classes, table.classDef)) {
            return this.applyLookupList(_rule.lookupRecords);
          }
        }
        break;
      case 3:
        if (this.coverageSequenceMatches(0, table.coverages)) {
          return this.applyLookupList(table.lookupRecords);
        }
        break;
    }
    return false;
  };
  _proto25.applyChainingContext = function applyChainingContext(table) {
    switch (table.version) {
      case 1:
        var index = this.coverageIndex(table.coverage);
        if (index === -1) {
          return false;
        }
        var set = table.chainRuleSets[index];
        for (var _iterator51 = _createForOfIteratorHelperLoose(set), _step51; !(_step51 = _iterator51()).done;) {
          var rule = _step51.value;
          if (this.sequenceMatches(-rule.backtrack.length, rule.backtrack) && this.sequenceMatches(1, rule.input) && this.sequenceMatches(1 + rule.input.length, rule.lookahead)) {
            return this.applyLookupList(rule.lookupRecords);
          }
        }
        break;
      case 2:
        if (this.coverageIndex(table.coverage) === -1) {
          return false;
        }
        index = this.getClassID(this.glyphIterator.cur.id, table.inputClassDef);
        var rules = table.chainClassSet[index];
        if (!rules) {
          return false;
        }
        for (var _iterator52 = _createForOfIteratorHelperLoose(rules), _step52; !(_step52 = _iterator52()).done;) {
          var _rule2 = _step52.value;
          if (this.classSequenceMatches(-_rule2.backtrack.length, _rule2.backtrack, table.backtrackClassDef) && this.classSequenceMatches(1, _rule2.input, table.inputClassDef) && this.classSequenceMatches(1 + _rule2.input.length, _rule2.lookahead, table.lookaheadClassDef)) {
            return this.applyLookupList(_rule2.lookupRecords);
          }
        }
        break;
      case 3:
        if (this.coverageSequenceMatches(-table.backtrackGlyphCount, table.backtrackCoverage) && this.coverageSequenceMatches(0, table.inputCoverage) && this.coverageSequenceMatches(table.inputGlyphCount, table.lookaheadCoverage)) {
          return this.applyLookupList(table.lookupRecords);
        }
        break;
    }
    return false;
  };
  return OTProcessor;
}();
var GlyphInfo = /*#__PURE__*/function () {
  function GlyphInfo(font, id, codePoints, features) {
    if (codePoints === void 0) {
      codePoints = [];
    }
    this._font = font;
    this.codePoints = codePoints;
    this.id = id;
    this.features = {};
    if (Array.isArray(features)) {
      for (var i = 0; i < features.length; i++) {
        var feature = features[i];
        this.features[feature] = true;
      }
    } else if (typeof features === 'object') {
      Object.assign(this.features, features);
    }
    this.ligatureID = null;
    this.ligatureComponent = null;
    this.isLigated = false;
    this.cursiveAttachment = null;
    this.markAttachment = null;
    this.shaperInfo = null;
    this.substituted = false;
    this.isMultiplied = false;
  }
  var _proto26 = GlyphInfo.prototype;
  _proto26.copy = function copy() {
    return new GlyphInfo(this._font, this.id, this.codePoints, this.features);
  };
  return _createClass(GlyphInfo, [{
    key: "id",
    get: function get() {
      return this._id;
    },
    set: function set(id) {
      this._id = id;
      this.substituted = true;
      var GDEF = this._font.GDEF;
      if (GDEF && GDEF.glyphClassDef) {
        // TODO: clean this up
        var classID = OTProcessor.prototype.getClassID(id, GDEF.glyphClassDef);
        this.isBase = classID === 1;
        this.isLigature = classID === 2;
        this.isMark = classID === 3;
        this.markAttachmentType = GDEF.markAttachClassDef ? OTProcessor.prototype.getClassID(id, GDEF.markAttachClassDef) : 0;
      } else {
        this.isMark = this.codePoints.length > 0 && this.codePoints.every(unicode.isMark);
        this.isBase = !this.isMark;
        this.isLigature = this.codePoints.length > 1;
        this.markAttachmentType = 0;
      }
    }
  }]);
}();
/**
 * This is a shaper for the Hangul script, used by the Korean language.
 * It does the following:
 *   - decompose if unsupported by the font:
 *     <LV>   -> <L,V>
 *     <LVT>  -> <L,V,T>
 *     <LV,T> -> <L,V,T>
 *
 *   - compose if supported by the font:
 *     <L,V>   -> <LV>
 *     <L,V,T> -> <LVT>
 *     <LV,T>  -> <LVT>
 *
 *   - reorder tone marks (S is any valid syllable):
 *     <S, M> -> <M, S>
 *
 *   - apply ljmo, vjmo, and tjmo OpenType features to decomposed Jamo sequences.
 *
 * This logic is based on the following documents:
 *   - http://www.microsoft.com/typography/OpenTypeDev/hangul/intro.htm
 *   - http://ktug.org/~nomos/harfbuzz-hangul/hangulshaper.pdf
 */
var HangulShaper = /*#__PURE__*/function (_DefaultShaper2) {
  function HangulShaper() {
    return _DefaultShaper2.apply(this, arguments) || this;
  }
  _inheritsLoose(HangulShaper, _DefaultShaper2);
  HangulShaper.planFeatures = function planFeatures(plan) {
    plan.add(['ljmo', 'vjmo', 'tjmo'], false);
  };
  HangulShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var state = 0;
    var i = 0;
    while (i < glyphs.length) {
      var action = void 0;
      var glyph = glyphs[i];
      var code = glyph.codePoints[0];
      var type = getType(code);
      var _STATE_TABLE$1$state$ = STATE_TABLE$1[state][type];
      action = _STATE_TABLE$1$state$[0];
      state = _STATE_TABLE$1$state$[1];
      switch (action) {
        case DECOMPOSE:
          // Decompose the composed syllable if it is not supported by the font.
          if (!plan.font.hasGlyphForCodePoint(code)) {
            i = decompose(glyphs, i, plan.font);
          }
          break;
        case COMPOSE:
          // Found a decomposed syllable. Try to compose if supported by the font.
          i = compose(glyphs, i, plan.font);
          break;
        case TONE_MARK:
          // Got a valid syllable, followed by a tone mark. Move the tone mark to the beginning of the syllable.
          reorderToneMark(glyphs, i, plan.font);
          break;
        case INVALID:
          // Tone mark has no valid syllable to attach to, so insert a dotted circle
          i = insertDottedCircle(glyphs, i, plan.font);
          break;
      }
      i++;
    }
  };
  return HangulShaper;
}(DefaultShaper);
_defineProperty(HangulShaper, "zeroMarkWidths", 'NONE');
var HANGUL_BASE = 0xac00;
var HANGUL_END = 0xd7a4;
var HANGUL_COUNT = HANGUL_END - HANGUL_BASE + 1;
var L_BASE = 0x1100; // lead

var V_BASE = 0x1161; // vowel

var T_BASE = 0x11a7; // trail

var L_COUNT = 19;
var V_COUNT = 21;
var T_COUNT = 28;
var L_END = L_BASE + L_COUNT - 1;
var V_END = V_BASE + V_COUNT - 1;
var T_END = T_BASE + T_COUNT - 1;
var DOTTED_CIRCLE = 0x25cc;
var isL = function isL(code) {
  return 0x1100 <= code && code <= 0x115f || 0xa960 <= code && code <= 0xa97c;
};
var isV = function isV(code) {
  return 0x1160 <= code && code <= 0x11a7 || 0xd7b0 <= code && code <= 0xd7c6;
};
var isT = function isT(code) {
  return 0x11a8 <= code && code <= 0x11ff || 0xd7cb <= code && code <= 0xd7fb;
};
var isTone = function isTone(code) {
  return 0x302e <= code && code <= 0x302f;
};
var isLVT = function isLVT(code) {
  return HANGUL_BASE <= code && code <= HANGUL_END;
};
var isLV = function isLV(code) {
  return code - HANGUL_BASE < HANGUL_COUNT && (code - HANGUL_BASE) % T_COUNT === 0;
};
var isCombiningL = function isCombiningL(code) {
  return L_BASE <= code && code <= L_END;
};
var isCombiningV = function isCombiningV(code) {
  return V_BASE <= code && code <= V_END;
};
var isCombiningT = function isCombiningT(code) {
  return 1 <= code && code <= T_END;
}; // Character categories

var X = 0; // Other character

var L = 1; // Leading consonant

var V = 2; // Medial vowel

var T = 3; // Trailing consonant

var LV = 4; // Composed <LV> syllable

var LVT = 5; // Composed <LVT> syllable

var M = 6; // Tone mark
// This function classifies a character using the above categories.

function getType(code) {
  if (isL(code)) {
    return L;
  }
  if (isV(code)) {
    return V;
  }
  if (isT(code)) {
    return T;
  }
  if (isLV(code)) {
    return LV;
  }
  if (isLVT(code)) {
    return LVT;
  }
  if (isTone(code)) {
    return M;
  }
  return X;
} // State machine actions

var NO_ACTION = 0;
var DECOMPOSE = 1;
var COMPOSE = 2;
var TONE_MARK = 4;
var INVALID = 5; // Build a state machine that accepts valid syllables, and applies actions along the way.
// The logic this is implementing is documented at the top of the file.

var STATE_TABLE$1 = [
//       X                 L                 V                T                  LV                LVT               M
// State 0: start state
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [INVALID, 0]],
// State 1: <L>
[[NO_ACTION, 0], [NO_ACTION, 1], [COMPOSE, 2], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [INVALID, 0]],
// State 2: <L,V> or <LV>
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [COMPOSE, 3], [DECOMPOSE, 2], [DECOMPOSE, 3], [TONE_MARK, 0]],
// State 3: <L,V,T> or <LVT>
[[NO_ACTION, 0], [NO_ACTION, 1], [NO_ACTION, 0], [NO_ACTION, 0], [DECOMPOSE, 2], [DECOMPOSE, 3], [TONE_MARK, 0]]];
function getGlyph(font, code, features) {
  return new GlyphInfo(font, font.glyphForCodePoint(code).id, [code], features);
}
function decompose(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyph.codePoints[0];
  var s = code - HANGUL_BASE;
  var t = T_BASE + s % T_COUNT;
  s = s / T_COUNT | 0;
  var l = L_BASE + s / V_COUNT | 0;
  var v = V_BASE + s % V_COUNT; // Don't decompose if all of the components are not available

  if (!font.hasGlyphForCodePoint(l) || !font.hasGlyphForCodePoint(v) || t !== T_BASE && !font.hasGlyphForCodePoint(t)) {
    return i;
  } // Replace the current glyph with decomposed L, V, and T glyphs,
  // and apply the proper OpenType features to each component.

  var ljmo = getGlyph(font, l, glyph.features);
  ljmo.features.ljmo = true;
  var vjmo = getGlyph(font, v, glyph.features);
  vjmo.features.vjmo = true;
  var insert = [ljmo, vjmo];
  if (t > T_BASE) {
    var tjmo = getGlyph(font, t, glyph.features);
    tjmo.features.tjmo = true;
    insert.push(tjmo);
  }
  glyphs.splice.apply(glyphs, [i, 1].concat(insert));
  return i + insert.length - 1;
}
function compose(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0];
  var type = getType(code);
  var prev = glyphs[i - 1].codePoints[0];
  var prevType = getType(prev); // Figure out what type of syllable we're dealing with

  var lv, ljmo, vjmo, tjmo;
  if (prevType === LV && type === T) {
    // <LV,T>
    lv = prev;
    tjmo = glyph;
  } else {
    if (type === V) {
      // <L,V>
      ljmo = glyphs[i - 1];
      vjmo = glyph;
    } else {
      // <L,V,T>
      ljmo = glyphs[i - 2];
      vjmo = glyphs[i - 1];
      tjmo = glyph;
    }
    var l = ljmo.codePoints[0];
    var v = vjmo.codePoints[0]; // Make sure L and V are combining characters

    if (isCombiningL(l) && isCombiningV(v)) {
      lv = HANGUL_BASE + ((l - L_BASE) * V_COUNT + (v - V_BASE)) * T_COUNT;
    }
  }
  var t = tjmo && tjmo.codePoints[0] || T_BASE;
  if (lv != null && (t === T_BASE || isCombiningT(t))) {
    var s = lv + (t - T_BASE); // Replace with a composed glyph if supported by the font,
    // otherwise apply the proper OpenType features to each component.

    if (font.hasGlyphForCodePoint(s)) {
      var del = prevType === V ? 3 : 2;
      glyphs.splice(i - del + 1, del, getGlyph(font, s, glyph.features));
      return i - del + 1;
    }
  } // Didn't compose (either a non-combining component or unsupported by font).

  if (ljmo) {
    ljmo.features.ljmo = true;
  }
  if (vjmo) {
    vjmo.features.vjmo = true;
  }
  if (tjmo) {
    tjmo.features.tjmo = true;
  }
  if (prevType === LV) {
    // Sequence was originally <L,V>, which got combined earlier.
    // Either the T was non-combining, or the LVT glyph wasn't supported.
    // Decompose the glyph again and apply OT features.
    decompose(glyphs, i - 1, font);
    return i + 1;
  }
  return i;
}
function getLength(code) {
  switch (getType(code)) {
    case LV:
    case LVT:
      return 1;
    case V:
      return 2;
    case T:
      return 3;
  }
}
function reorderToneMark(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0]; // Move tone mark to the beginning of the previous syllable, unless it is zero width

  if (font.glyphForCodePoint(code).advanceWidth === 0) {
    return;
  }
  var prev = glyphs[i - 1].codePoints[0];
  var len = getLength(prev);
  glyphs.splice(i, 1);
  return glyphs.splice(i - len, 0, glyph);
}
function insertDottedCircle(glyphs, i, font) {
  var glyph = glyphs[i];
  var code = glyphs[i].codePoints[0];
  if (font.hasGlyphForCodePoint(DOTTED_CIRCLE)) {
    var dottedCircle = getGlyph(font, DOTTED_CIRCLE, glyph.features); // If the tone mark is zero width, insert the dotted circle before, otherwise after

    var idx = font.glyphForCodePoint(code).advanceWidth === 0 ? i : i + 1;
    glyphs.splice(idx, 0, dottedCircle);
    i++;
  }
  return i;
}
var stateTable = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 11, 12, 13, 14, 15, 16, 17], [0, 0, 0, 18, 19, 20, 21, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 28, 29, 30, 31, 32, 33, 0, 34, 0, 0, 35, 36, 0, 0, 37, 0], [0, 0, 0, 38, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 39, 0, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 43, 44, 44, 8, 9, 0, 0, 0, 0, 12, 43, 0, 0, 0, 0], [0, 0, 0, 0, 43, 44, 44, 8, 9, 0, 0, 0, 0, 0, 43, 0, 0, 0, 0], [0, 0, 0, 45, 46, 47, 48, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 50, 0, 0, 51, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 52, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 53, 54, 55, 56, 57, 58, 0, 59, 0, 0, 60, 61, 0, 0, 62, 0], [0, 0, 0, 4, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 63, 64, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 63, 0, 0], [0, 2, 3, 4, 5, 6, 7, 8, 9, 0, 10, 11, 11, 12, 13, 0, 2, 16, 0], [0, 0, 0, 18, 65, 20, 21, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 0, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 0, 0], [0, 0, 0, 69, 0, 70, 70, 0, 71, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 73, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 75, 0, 0, 0, 76, 77, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 79, 80, 80, 22, 23, 0, 0, 0, 0, 25, 79, 0, 0, 0, 0], [0, 0, 0, 18, 19, 20, 74, 22, 23, 0, 24, 0, 0, 25, 26, 0, 0, 27, 0], [0, 0, 0, 81, 82, 83, 84, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 86, 0, 0, 87, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 88, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 18, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 89, 90, 0, 0, 76, 77, 0, 23, 0, 24, 0, 0, 0, 78, 0, 89, 0, 0], [0, 0, 0, 0, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 0, 0], [0, 0, 0, 94, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 96, 0, 0, 0, 97, 98, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 35, 100, 0, 0, 0, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 0, 100, 0, 0, 0, 0], [0, 0, 0, 102, 103, 104, 105, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 107, 0, 0, 108, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 109, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 28, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 110, 111, 0, 0, 97, 98, 0, 33, 0, 34, 0, 0, 0, 99, 0, 110, 0, 0], [0, 0, 0, 0, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 0, 0], [0, 0, 0, 0, 5, 7, 7, 8, 9, 0, 10, 0, 0, 0, 13, 0, 0, 16, 0], [0, 0, 0, 115, 116, 117, 118, 8, 9, 0, 10, 0, 0, 119, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 39, 0, 122, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 124, 64, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 124, 0, 0], [0, 39, 0, 0, 0, 121, 125, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 126, 126, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 46, 47, 48, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 47, 47, 49, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 127, 127, 49, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 128, 127, 127, 49, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 129, 130, 131, 132, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 50, 0, 0, 0, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 134, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 135, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 136, 0, 0, 0, 137, 138, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 140, 141, 141, 57, 58, 0, 0, 0, 0, 60, 140, 0, 0, 0, 0], [0, 0, 0, 0, 140, 141, 141, 57, 58, 0, 0, 0, 0, 0, 140, 0, 0, 0, 0], [0, 0, 0, 142, 143, 144, 145, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 147, 0, 0, 148, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 149, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 53, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 150, 151, 0, 0, 137, 138, 0, 58, 0, 59, 0, 0, 0, 139, 0, 150, 0, 0], [0, 0, 0, 0, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 0, 0], [0, 0, 0, 155, 116, 156, 157, 8, 9, 0, 10, 0, 0, 158, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 75, 3, 4, 5, 159, 160, 8, 161, 0, 162, 0, 11, 12, 163, 0, 75, 16, 0], [0, 0, 0, 0, 0, 40, 164, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 0, 165, 0, 0, 0, 0], [0, 124, 64, 0, 0, 40, 164, 0, 9, 0, 10, 0, 0, 0, 42, 0, 124, 0, 0], [0, 0, 0, 0, 0, 70, 70, 0, 71, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 71, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 166, 0, 0, 167, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 168, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 19, 74, 74, 22, 23, 0, 24, 0, 0, 0, 26, 0, 0, 27, 0], [0, 0, 0, 0, 79, 80, 80, 22, 23, 0, 0, 0, 0, 0, 79, 0, 0, 0, 0], [0, 0, 0, 169, 170, 171, 172, 22, 23, 0, 24, 0, 0, 173, 174, 0, 0, 27, 0], [0, 0, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 75, 0, 176, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 178, 90, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 178, 0, 0], [0, 75, 0, 0, 0, 175, 179, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 180, 180, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 82, 83, 84, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 83, 83, 85, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 181, 181, 85, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 182, 181, 181, 85, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 183, 184, 185, 186, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 86, 0, 0, 0, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 188, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 189, 170, 190, 191, 22, 23, 0, 24, 0, 0, 192, 174, 0, 0, 27, 0], [0, 0, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 76, 193, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 0, 194, 0, 0, 0, 0], [0, 178, 90, 0, 0, 76, 193, 0, 23, 0, 24, 0, 0, 0, 78, 0, 178, 0, 0], [0, 0, 0, 0, 29, 95, 31, 32, 33, 0, 34, 0, 0, 0, 36, 0, 0, 37, 0], [0, 0, 0, 0, 100, 101, 101, 32, 33, 0, 0, 0, 0, 0, 100, 0, 0, 0, 0], [0, 0, 0, 195, 196, 197, 198, 32, 33, 0, 34, 0, 0, 199, 200, 0, 0, 37, 0], [0, 0, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 96, 0, 202, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 204, 111, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 204, 0, 0], [0, 96, 0, 0, 0, 201, 205, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 206, 206, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 103, 104, 105, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 104, 104, 106, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 207, 207, 106, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 208, 207, 207, 106, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 209, 210, 211, 212, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 107, 0, 0, 0, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 214, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 215, 196, 216, 217, 32, 33, 0, 34, 0, 0, 218, 200, 0, 0, 37, 0], [0, 0, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 97, 219, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 0, 220, 0, 0, 0, 0], [0, 204, 111, 0, 0, 97, 219, 0, 33, 0, 34, 0, 0, 0, 99, 0, 204, 0, 0], [0, 0, 0, 221, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 223, 0, 0, 0, 40, 224, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 119, 225, 0, 0, 0, 0], [0, 0, 0, 115, 116, 117, 222, 8, 9, 0, 10, 0, 0, 119, 120, 0, 0, 16, 0], [0, 0, 0, 115, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 226, 64, 0, 0, 40, 224, 0, 9, 0, 10, 0, 0, 0, 42, 0, 226, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 39, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 44, 44, 8, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 228, 229, 0, 9, 0, 10, 0, 0, 230, 0, 0, 0, 0, 0], [0, 39, 0, 122, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 39, 0, 0], [0, 0, 0, 0, 0, 0, 0, 8, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 231, 231, 49, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 232, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 130, 131, 132, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 131, 131, 133, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 233, 233, 133, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 234, 233, 233, 133, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 235, 236, 237, 238, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 54, 56, 56, 57, 58, 0, 59, 0, 0, 0, 61, 0, 0, 62, 0], [0, 0, 0, 240, 241, 242, 243, 57, 58, 0, 59, 0, 0, 244, 245, 0, 0, 62, 0], [0, 0, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 136, 0, 247, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 249, 151, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 249, 0, 0], [0, 136, 0, 0, 0, 246, 250, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 251, 251, 57, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 143, 144, 145, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 144, 144, 146, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 252, 252, 146, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 253, 252, 252, 146, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 254, 255, 256, 257, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 147, 0, 0, 0, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 259, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 260, 241, 261, 262, 57, 58, 0, 59, 0, 0, 263, 245, 0, 0, 62, 0], [0, 0, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 137, 264, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 0, 265, 0, 0, 0, 0], [0, 249, 151, 0, 0, 137, 264, 0, 58, 0, 59, 0, 0, 0, 139, 0, 249, 0, 0], [0, 0, 0, 221, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 158, 225, 0, 0, 0, 0], [0, 0, 0, 155, 116, 156, 222, 8, 9, 0, 10, 0, 0, 158, 120, 0, 0, 16, 0], [0, 0, 0, 155, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 43, 266, 266, 8, 161, 0, 24, 0, 0, 12, 267, 0, 0, 0, 0], [0, 75, 0, 176, 43, 268, 268, 269, 161, 0, 24, 0, 0, 0, 267, 0, 75, 0, 0], [0, 0, 0, 0, 0, 270, 0, 0, 271, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 272, 0, 0, 0, 0, 0, 0, 0, 0], [0, 273, 274, 0, 0, 40, 41, 0, 9, 0, 10, 0, 0, 0, 42, 0, 273, 0, 0], [0, 0, 0, 40, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 121, 275, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 166, 0, 0, 0, 0, 72, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 276, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 277, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 279, 0, 0, 0, 76, 280, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 173, 281, 0, 0, 0, 0], [0, 0, 0, 169, 170, 171, 278, 22, 23, 0, 24, 0, 0, 173, 174, 0, 0, 27, 0], [0, 0, 0, 169, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 282, 90, 0, 0, 76, 280, 0, 23, 0, 24, 0, 0, 0, 78, 0, 282, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 75, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 80, 80, 22, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 284, 285, 0, 23, 0, 24, 0, 0, 286, 0, 0, 0, 0, 0], [0, 75, 0, 176, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 75, 0, 0], [0, 0, 0, 0, 0, 0, 0, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 287, 287, 85, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 288, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 184, 185, 186, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 185, 185, 187, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 289, 289, 187, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 290, 289, 289, 187, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 291, 292, 293, 294, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 277, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 192, 281, 0, 0, 0, 0], [0, 0, 0, 189, 170, 190, 278, 22, 23, 0, 24, 0, 0, 192, 174, 0, 0, 27, 0], [0, 0, 0, 189, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 76, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 175, 296, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 297, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 299, 0, 0, 0, 97, 300, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 199, 301, 0, 0, 0, 0], [0, 0, 0, 195, 196, 197, 298, 32, 33, 0, 34, 0, 0, 199, 200, 0, 0, 37, 0], [0, 0, 0, 195, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 302, 111, 0, 0, 97, 300, 0, 33, 0, 34, 0, 0, 0, 99, 0, 302, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 96, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 101, 101, 32, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 304, 305, 0, 33, 0, 34, 0, 0, 306, 0, 0, 0, 0, 0], [0, 96, 0, 202, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 96, 0, 0], [0, 0, 0, 0, 0, 0, 0, 32, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 307, 307, 106, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 308, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 210, 211, 212, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 211, 211, 213, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 309, 309, 213, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 310, 309, 309, 213, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 311, 312, 313, 314, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 297, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 218, 301, 0, 0, 0, 0], [0, 0, 0, 215, 196, 216, 298, 32, 33, 0, 34, 0, 0, 218, 200, 0, 0, 37, 0], [0, 0, 0, 215, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 97, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 201, 316, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 116, 222, 222, 8, 9, 0, 10, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 9, 0, 0, 0, 0, 0, 225, 0, 0, 0, 0], [0, 0, 0, 317, 318, 319, 320, 8, 9, 0, 10, 0, 0, 321, 322, 0, 0, 16, 0], [0, 223, 0, 323, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 223, 0, 0, 0, 121, 324, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 325, 318, 326, 327, 8, 9, 0, 10, 0, 0, 328, 322, 0, 0, 16, 0], [0, 0, 0, 64, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 9, 0, 0, 0, 0, 230, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 228, 121, 0, 9, 0, 10, 0, 0, 230, 0, 0, 0, 0, 0], [0, 0, 0, 227, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 49, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 46, 0, 0], [0, 0, 0, 0, 0, 329, 329, 133, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 330, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 236, 237, 238, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 237, 237, 239, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 331, 331, 239, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 332, 331, 331, 239, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 333, 40, 121, 334, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 335, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 337, 0, 0, 0, 137, 338, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 244, 339, 0, 0, 0, 0], [0, 0, 0, 240, 241, 242, 336, 57, 58, 0, 59, 0, 0, 244, 245, 0, 0, 62, 0], [0, 0, 0, 240, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 340, 151, 0, 0, 137, 338, 0, 58, 0, 59, 0, 0, 0, 139, 0, 340, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 136, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 141, 141, 57, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 342, 343, 0, 58, 0, 59, 0, 0, 344, 0, 0, 0, 0, 0], [0, 136, 0, 247, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 136, 0, 0], [0, 0, 0, 0, 0, 0, 0, 57, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 345, 345, 146, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 346, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 255, 256, 257, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 256, 256, 258, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 347, 347, 258, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 348, 347, 347, 258, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 349, 350, 351, 352, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 335, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 263, 339, 0, 0, 0, 0], [0, 0, 0, 260, 241, 261, 336, 57, 58, 0, 59, 0, 0, 263, 245, 0, 0, 62, 0], [0, 0, 0, 260, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 137, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 246, 354, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 126, 126, 8, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 355, 90, 0, 0, 121, 125, 0, 9, 0, 10, 0, 0, 0, 42, 0, 355, 0, 0], [0, 0, 0, 0, 0, 356, 356, 269, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 357, 358, 359, 360, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 270, 0, 0, 0, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 363, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 364, 116, 365, 366, 8, 161, 0, 162, 0, 0, 367, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 40, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 170, 278, 278, 22, 23, 0, 24, 0, 0, 0, 174, 0, 0, 27, 0], [0, 0, 0, 0, 281, 80, 80, 22, 23, 0, 0, 0, 0, 0, 281, 0, 0, 0, 0], [0, 0, 0, 369, 370, 371, 372, 22, 23, 0, 24, 0, 0, 373, 374, 0, 0, 27, 0], [0, 279, 0, 375, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 279, 0, 0, 0, 175, 376, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 377, 370, 378, 379, 22, 23, 0, 24, 0, 0, 380, 374, 0, 0, 27, 0], [0, 0, 0, 90, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 23, 0, 0, 0, 0, 286, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 284, 175, 0, 23, 0, 24, 0, 0, 286, 0, 0, 0, 0, 0], [0, 0, 0, 283, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 85, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 82, 0, 0], [0, 0, 0, 0, 0, 381, 381, 187, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 382, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 292, 293, 294, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 293, 293, 295, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 0, 383, 383, 295, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 384, 383, 383, 295, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 385, 76, 175, 386, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 76, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 196, 298, 298, 32, 33, 0, 34, 0, 0, 0, 200, 0, 0, 37, 0], [0, 0, 0, 0, 301, 101, 101, 32, 33, 0, 0, 0, 0, 0, 301, 0, 0, 0, 0], [0, 0, 0, 387, 388, 389, 390, 32, 33, 0, 34, 0, 0, 391, 392, 0, 0, 37, 0], [0, 299, 0, 393, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 299, 0, 0, 0, 201, 394, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 395, 388, 396, 397, 32, 33, 0, 34, 0, 0, 398, 392, 0, 0, 37, 0], [0, 0, 0, 111, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 33, 0, 0, 0, 0, 306, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 304, 201, 0, 33, 0, 34, 0, 0, 306, 0, 0, 0, 0, 0], [0, 0, 0, 303, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 106, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 103, 0, 0], [0, 0, 0, 0, 0, 399, 399, 213, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 400, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 312, 313, 314, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 313, 313, 315, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 0, 401, 401, 315, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 402, 401, 401, 315, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 403, 97, 201, 404, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 97, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 405, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 407, 0, 0, 0, 40, 408, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 321, 409, 0, 0, 0, 0], [0, 0, 0, 317, 318, 319, 406, 8, 9, 0, 10, 0, 0, 321, 322, 0, 0, 16, 0], [0, 0, 0, 317, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 410, 64, 0, 0, 40, 408, 0, 9, 0, 10, 0, 0, 0, 42, 0, 410, 0, 0], [0, 223, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 223, 0, 323, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 223, 0, 0], [0, 0, 0, 405, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 328, 409, 0, 0, 0, 0], [0, 0, 0, 325, 318, 326, 406, 8, 9, 0, 10, 0, 0, 328, 322, 0, 0, 16, 0], [0, 0, 0, 325, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 133, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 130, 0, 0], [0, 0, 0, 0, 0, 411, 411, 239, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 412, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 40, 121, 334, 0, 9, 0, 10, 0, 0, 0, 42, 0, 0, 0, 0], [0, 0, 0, 0, 413, 0, 0, 0, 9, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 241, 336, 336, 57, 58, 0, 59, 0, 0, 0, 245, 0, 0, 62, 0], [0, 0, 0, 0, 339, 141, 141, 57, 58, 0, 0, 0, 0, 0, 339, 0, 0, 0, 0], [0, 0, 0, 414, 415, 416, 417, 57, 58, 0, 59, 0, 0, 418, 419, 0, 0, 62, 0], [0, 337, 0, 420, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 337, 0, 0, 0, 246, 421, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 422, 415, 423, 424, 57, 58, 0, 59, 0, 0, 425, 419, 0, 0, 62, 0], [0, 0, 0, 151, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 58, 0, 0, 0, 0, 344, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 342, 246, 0, 58, 0, 59, 0, 0, 344, 0, 0, 0, 0, 0], [0, 0, 0, 341, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 146, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 143, 0, 0], [0, 0, 0, 0, 0, 426, 426, 258, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 427, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 350, 351, 352, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 351, 351, 353, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 0, 428, 428, 353, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 429, 428, 428, 353, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 430, 137, 246, 431, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 137, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 432, 116, 433, 434, 8, 161, 0, 162, 0, 0, 435, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 180, 180, 269, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 358, 359, 360, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 359, 359, 361, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 436, 436, 361, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 437, 436, 436, 361, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 438, 439, 440, 441, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 443, 274, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 443, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 444, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 367, 225, 0, 0, 0, 0], [0, 0, 0, 364, 116, 365, 445, 8, 161, 0, 162, 0, 0, 367, 120, 0, 0, 16, 0], [0, 0, 0, 364, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 0, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 446, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 448, 0, 0, 0, 76, 449, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 373, 450, 0, 0, 0, 0], [0, 0, 0, 369, 370, 371, 447, 22, 23, 0, 24, 0, 0, 373, 374, 0, 0, 27, 0], [0, 0, 0, 369, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 451, 90, 0, 0, 76, 449, 0, 23, 0, 24, 0, 0, 0, 78, 0, 451, 0, 0], [0, 279, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 279, 0, 375, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 279, 0, 0], [0, 0, 0, 446, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 380, 450, 0, 0, 0, 0], [0, 0, 0, 377, 370, 378, 447, 22, 23, 0, 24, 0, 0, 380, 374, 0, 0, 27, 0], [0, 0, 0, 377, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 187, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 184, 0, 0], [0, 0, 0, 0, 0, 452, 452, 295, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 453, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 76, 175, 386, 0, 23, 0, 24, 0, 0, 0, 78, 0, 0, 0, 0], [0, 0, 0, 0, 454, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 455, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 457, 0, 0, 0, 97, 458, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 391, 459, 0, 0, 0, 0], [0, 0, 0, 387, 388, 389, 456, 32, 33, 0, 34, 0, 0, 391, 392, 0, 0, 37, 0], [0, 0, 0, 387, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 460, 111, 0, 0, 97, 458, 0, 33, 0, 34, 0, 0, 0, 99, 0, 460, 0, 0], [0, 299, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 299, 0, 393, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 299, 0, 0], [0, 0, 0, 455, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 398, 459, 0, 0, 0, 0], [0, 0, 0, 395, 388, 396, 456, 32, 33, 0, 34, 0, 0, 398, 392, 0, 0, 37, 0], [0, 0, 0, 395, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 213, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 210, 0, 0], [0, 0, 0, 0, 0, 461, 461, 315, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 462, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 97, 201, 404, 0, 33, 0, 34, 0, 0, 0, 99, 0, 0, 0, 0], [0, 0, 0, 0, 463, 0, 0, 0, 33, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 318, 406, 406, 8, 9, 0, 10, 0, 0, 0, 322, 0, 0, 16, 0], [0, 0, 0, 0, 409, 44, 44, 8, 9, 0, 0, 0, 0, 0, 409, 0, 0, 0, 0], [0, 0, 0, 464, 465, 466, 467, 8, 9, 0, 10, 0, 0, 468, 469, 0, 0, 16, 0], [0, 407, 0, 470, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 407, 0, 0, 0, 121, 471, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 472, 465, 473, 474, 8, 9, 0, 10, 0, 0, 475, 469, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 239, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 236, 0, 0], [0, 0, 0, 0, 0, 0, 476, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 477, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 479, 0, 0, 0, 137, 480, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 418, 481, 0, 0, 0, 0], [0, 0, 0, 414, 415, 416, 478, 57, 58, 0, 59, 0, 0, 418, 419, 0, 0, 62, 0], [0, 0, 0, 414, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 482, 151, 0, 0, 137, 480, 0, 58, 0, 59, 0, 0, 0, 139, 0, 482, 0, 0], [0, 337, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 337, 0, 420, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 337, 0, 0], [0, 0, 0, 477, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 425, 481, 0, 0, 0, 0], [0, 0, 0, 422, 415, 423, 478, 57, 58, 0, 59, 0, 0, 425, 419, 0, 0, 62, 0], [0, 0, 0, 422, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 258, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 255, 0, 0], [0, 0, 0, 0, 0, 483, 483, 353, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 484, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 137, 246, 431, 0, 58, 0, 59, 0, 0, 0, 139, 0, 0, 0, 0], [0, 0, 0, 0, 485, 0, 0, 0, 58, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 444, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 435, 225, 0, 0, 0, 0], [0, 0, 0, 432, 116, 433, 445, 8, 161, 0, 162, 0, 0, 435, 120, 0, 0, 16, 0], [0, 0, 0, 432, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 0, 486, 486, 361, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 487, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 439, 440, 441, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 440, 440, 442, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 488, 488, 442, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 489, 488, 488, 442, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 490, 491, 492, 493, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 495, 0, 496, 497, 0, 161, 0, 162, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 0, 116, 445, 445, 8, 161, 0, 162, 0, 0, 0, 120, 0, 0, 16, 0], [0, 0, 0, 0, 225, 44, 44, 8, 161, 0, 0, 0, 0, 0, 225, 0, 0, 0, 0], [0, 0, 0, 0, 370, 447, 447, 22, 23, 0, 24, 0, 0, 0, 374, 0, 0, 27, 0], [0, 0, 0, 0, 450, 80, 80, 22, 23, 0, 0, 0, 0, 0, 450, 0, 0, 0, 0], [0, 0, 0, 499, 500, 501, 502, 22, 23, 0, 24, 0, 0, 503, 504, 0, 0, 27, 0], [0, 448, 0, 505, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 448, 0, 0, 0, 175, 506, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 507, 500, 508, 509, 22, 23, 0, 24, 0, 0, 510, 504, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 295, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 292, 0, 0], [0, 0, 0, 0, 0, 0, 511, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 388, 456, 456, 32, 33, 0, 34, 0, 0, 0, 392, 0, 0, 37, 0], [0, 0, 0, 0, 459, 101, 101, 32, 33, 0, 0, 0, 0, 0, 459, 0, 0, 0, 0], [0, 0, 0, 512, 513, 514, 515, 32, 33, 0, 34, 0, 0, 516, 517, 0, 0, 37, 0], [0, 457, 0, 518, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 457, 0, 0, 0, 201, 519, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 520, 513, 521, 522, 32, 33, 0, 34, 0, 0, 523, 517, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 315, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 312, 0, 0], [0, 0, 0, 0, 0, 0, 524, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 525, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 527, 0, 0, 0, 40, 528, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 468, 529, 0, 0, 0, 0], [0, 0, 0, 464, 465, 466, 526, 8, 9, 0, 10, 0, 0, 468, 469, 0, 0, 16, 0], [0, 0, 0, 464, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 530, 64, 0, 0, 40, 528, 0, 9, 0, 10, 0, 0, 0, 42, 0, 530, 0, 0], [0, 407, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 407, 0, 470, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 407, 0, 0], [0, 0, 0, 525, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 475, 529, 0, 0, 0, 0], [0, 0, 0, 472, 465, 473, 526, 8, 9, 0, 10, 0, 0, 475, 469, 0, 0, 16, 0], [0, 0, 0, 472, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 40, 0, 0], [0, 0, 0, 0, 415, 478, 478, 57, 58, 0, 59, 0, 0, 0, 419, 0, 0, 62, 0], [0, 0, 0, 0, 481, 141, 141, 57, 58, 0, 0, 0, 0, 0, 481, 0, 0, 0, 0], [0, 0, 0, 531, 532, 533, 534, 57, 58, 0, 59, 0, 0, 535, 536, 0, 0, 62, 0], [0, 479, 0, 537, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 479, 0, 0, 0, 246, 538, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 539, 532, 540, 541, 57, 58, 0, 59, 0, 0, 542, 536, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 353, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 350, 0, 0], [0, 0, 0, 0, 0, 0, 543, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 361, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 358, 0, 0], [0, 0, 0, 0, 0, 544, 544, 442, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 545, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 491, 492, 493, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 492, 492, 494, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 546, 546, 494, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 547, 546, 546, 494, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 548, 549, 368, 550, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 274, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 161, 0, 0, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 495, 0, 496, 368, 0, 161, 0, 162, 0, 0, 498, 0, 0, 0, 0, 0], [0, 0, 0, 495, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 551, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 553, 0, 0, 0, 76, 554, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 503, 555, 0, 0, 0, 0], [0, 0, 0, 499, 500, 501, 552, 22, 23, 0, 24, 0, 0, 503, 504, 0, 0, 27, 0], [0, 0, 0, 499, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 556, 90, 0, 0, 76, 554, 0, 23, 0, 24, 0, 0, 0, 78, 0, 556, 0, 0], [0, 448, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 448, 0, 505, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 448, 0, 0], [0, 0, 0, 551, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 510, 555, 0, 0, 0, 0], [0, 0, 0, 507, 500, 508, 552, 22, 23, 0, 24, 0, 0, 510, 504, 0, 0, 27, 0], [0, 0, 0, 507, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 76, 0, 0], [0, 0, 0, 557, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 559, 0, 0, 0, 97, 560, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 516, 561, 0, 0, 0, 0], [0, 0, 0, 512, 513, 514, 558, 32, 33, 0, 34, 0, 0, 516, 517, 0, 0, 37, 0], [0, 0, 0, 512, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 562, 111, 0, 0, 97, 560, 0, 33, 0, 34, 0, 0, 0, 99, 0, 562, 0, 0], [0, 457, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 457, 0, 518, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 457, 0, 0], [0, 0, 0, 557, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 523, 561, 0, 0, 0, 0], [0, 0, 0, 520, 513, 521, 558, 32, 33, 0, 34, 0, 0, 523, 517, 0, 0, 37, 0], [0, 0, 0, 520, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 97, 0, 0], [0, 0, 0, 0, 465, 526, 526, 8, 9, 0, 10, 0, 0, 0, 469, 0, 0, 16, 0], [0, 0, 0, 0, 529, 44, 44, 8, 9, 0, 0, 0, 0, 0, 529, 0, 0, 0, 0], [0, 0, 0, 563, 66, 564, 565, 8, 9, 0, 10, 0, 0, 566, 68, 0, 0, 16, 0], [0, 527, 0, 567, 0, 123, 123, 8, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 527, 0, 0, 0, 121, 568, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 569, 66, 570, 571, 8, 9, 0, 10, 0, 0, 572, 68, 0, 0, 16, 0], [0, 0, 0, 573, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 575, 0, 0, 0, 137, 576, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 535, 577, 0, 0, 0, 0], [0, 0, 0, 531, 532, 533, 574, 57, 58, 0, 59, 0, 0, 535, 536, 0, 0, 62, 0], [0, 0, 0, 531, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 578, 151, 0, 0, 137, 576, 0, 58, 0, 59, 0, 0, 0, 139, 0, 578, 0, 0], [0, 479, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 479, 0, 537, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 479, 0, 0], [0, 0, 0, 573, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 542, 577, 0, 0, 0, 0], [0, 0, 0, 539, 532, 540, 574, 57, 58, 0, 59, 0, 0, 542, 536, 0, 0, 62, 0], [0, 0, 0, 539, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 137, 0, 0], [0, 0, 0, 0, 0, 0, 0, 442, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 439, 0, 0], [0, 0, 0, 0, 0, 579, 579, 494, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 580, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 549, 368, 550, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 0, 368, 368, 0, 161, 0, 162, 0, 0, 0, 362, 0, 0, 0, 0], [0, 0, 0, 0, 581, 0, 0, 0, 161, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 500, 552, 552, 22, 23, 0, 24, 0, 0, 0, 504, 0, 0, 27, 0], [0, 0, 0, 0, 555, 80, 80, 22, 23, 0, 0, 0, 0, 0, 555, 0, 0, 0, 0], [0, 0, 0, 582, 91, 583, 584, 22, 23, 0, 24, 0, 0, 585, 93, 0, 0, 27, 0], [0, 553, 0, 586, 0, 177, 177, 22, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 553, 0, 0, 0, 175, 587, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 588, 91, 589, 590, 22, 23, 0, 24, 0, 0, 591, 93, 0, 0, 27, 0], [0, 0, 0, 0, 513, 558, 558, 32, 33, 0, 34, 0, 0, 0, 517, 0, 0, 37, 0], [0, 0, 0, 0, 561, 101, 101, 32, 33, 0, 0, 0, 0, 0, 561, 0, 0, 0, 0], [0, 0, 0, 592, 112, 593, 594, 32, 33, 0, 34, 0, 0, 595, 114, 0, 0, 37, 0], [0, 559, 0, 596, 0, 203, 203, 32, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 559, 0, 0, 0, 201, 597, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 598, 112, 599, 600, 32, 33, 0, 34, 0, 0, 601, 114, 0, 0, 37, 0], [0, 0, 0, 602, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 566, 165, 0, 0, 0, 0], [0, 0, 0, 563, 66, 564, 67, 8, 9, 0, 10, 0, 0, 566, 68, 0, 0, 16, 0], [0, 0, 0, 563, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 527, 0, 0, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 527, 0, 567, 0, 121, 121, 0, 9, 0, 10, 0, 0, 0, 42, 0, 527, 0, 0], [0, 0, 0, 602, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 165, 44, 44, 8, 9, 0, 0, 0, 0, 572, 165, 0, 0, 0, 0], [0, 0, 0, 569, 66, 570, 67, 8, 9, 0, 10, 0, 0, 572, 68, 0, 0, 16, 0], [0, 0, 0, 569, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 0, 532, 574, 574, 57, 58, 0, 59, 0, 0, 0, 536, 0, 0, 62, 0], [0, 0, 0, 0, 577, 141, 141, 57, 58, 0, 0, 0, 0, 0, 577, 0, 0, 0, 0], [0, 0, 0, 603, 152, 604, 605, 57, 58, 0, 59, 0, 0, 606, 154, 0, 0, 62, 0], [0, 575, 0, 607, 0, 248, 248, 57, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 575, 0, 0, 0, 246, 608, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 609, 152, 610, 611, 57, 58, 0, 59, 0, 0, 612, 154, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 494, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 491, 0, 0], [0, 0, 0, 0, 0, 0, 613, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 614, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 585, 194, 0, 0, 0, 0], [0, 0, 0, 582, 91, 583, 92, 22, 23, 0, 24, 0, 0, 585, 93, 0, 0, 27, 0], [0, 0, 0, 582, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 553, 0, 0, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 553, 0, 586, 0, 175, 175, 0, 23, 0, 24, 0, 0, 0, 78, 0, 553, 0, 0], [0, 0, 0, 614, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 194, 80, 80, 22, 23, 0, 0, 0, 0, 591, 194, 0, 0, 0, 0], [0, 0, 0, 588, 91, 589, 92, 22, 23, 0, 24, 0, 0, 591, 93, 0, 0, 27, 0], [0, 0, 0, 588, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 615, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 595, 220, 0, 0, 0, 0], [0, 0, 0, 592, 112, 593, 113, 32, 33, 0, 34, 0, 0, 595, 114, 0, 0, 37, 0], [0, 0, 0, 592, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 559, 0, 0, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 559, 0, 596, 0, 201, 201, 0, 33, 0, 34, 0, 0, 0, 99, 0, 559, 0, 0], [0, 0, 0, 615, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 220, 101, 101, 32, 33, 0, 0, 0, 0, 601, 220, 0, 0, 0, 0], [0, 0, 0, 598, 112, 599, 113, 32, 33, 0, 34, 0, 0, 601, 114, 0, 0, 37, 0], [0, 0, 0, 598, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 66, 67, 67, 8, 9, 0, 10, 0, 0, 0, 68, 0, 0, 16, 0], [0, 0, 0, 616, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 606, 265, 0, 0, 0, 0], [0, 0, 0, 603, 152, 604, 153, 57, 58, 0, 59, 0, 0, 606, 154, 0, 0, 62, 0], [0, 0, 0, 603, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 575, 0, 0, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 575, 0, 607, 0, 246, 246, 0, 58, 0, 59, 0, 0, 0, 139, 0, 575, 0, 0], [0, 0, 0, 616, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 265, 141, 141, 57, 58, 0, 0, 0, 0, 612, 265, 0, 0, 0, 0], [0, 0, 0, 609, 152, 610, 153, 57, 58, 0, 59, 0, 0, 612, 154, 0, 0, 62, 0], [0, 0, 0, 609, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 549, 0, 0], [0, 0, 0, 0, 91, 92, 92, 22, 23, 0, 24, 0, 0, 0, 93, 0, 0, 27, 0], [0, 0, 0, 0, 112, 113, 113, 32, 33, 0, 34, 0, 0, 0, 114, 0, 0, 37, 0], [0, 0, 0, 0, 152, 153, 153, 57, 58, 0, 59, 0, 0, 0, 154, 0, 0, 62, 0]];
var accepting = [false, true, true, true, true, true, false, false, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, false, false, true, true, true, true, true, true, true, true, true, true, false, true, true, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, false, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, true, true, true, false, true, false, true, true, false, false, true, true, true, true, true, true, true, false, true, true, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, true, false, true, false, true, true, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, false, true, false, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, true, true, true, false, true, true, false, false, false, false, true, true, false, false, true, true, true, false, true, true, false, false, true, false, true, true, false, true, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, true, true, false, false, false, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, true, false, true, false, true, true, false, false, true, true, false, false, true, true, true, false, true, false, true, true, true, true, false, false, false, true, false, true, true, true, true, false, false, false, true, true, false, true, true, true, true, true, true, false, true, true, false, true, false, true, true, true, true, false, false, false, false, false, false, false, true, true, false, false, true, true, false, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, true, true, false, true, true, true, true, true, true, false, true, true, false, true, false, true, true, true, true, true, true, false, true, true, true, true, true, true, false, true, true, false, false, false, false, false, true, true, false, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, true, true, true, false, true, true, true, false, true, true, true, true, false, false, false, true, false, true, true, true, true, true, false, true, true, true, false, true, true, true, true, true, false, true, true, true, true, false, true, true, true, true, true, false, true, true, false, true, true, true];
var tags = [[], ["broken_cluster"], ["consonant_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable"], ["broken_cluster"], ["symbol_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["symbol_cluster"], [], ["symbol_cluster"], ["symbol_cluster"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], [], ["broken_cluster"], ["broken_cluster"], [], [], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["broken_cluster"], ["symbol_cluster"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], [], ["consonant_syllable"], ["consonant_syllable"], [], [], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], [], ["vowel_syllable"], ["vowel_syllable"], [], [], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], [], [], [], ["broken_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], [], ["standalone_cluster"], ["standalone_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], [], [], [], ["consonant_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], [], [], [], ["vowel_syllable"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], [], [], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], ["standalone_cluster"], [], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], [], [], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], [], [], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], [], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], [], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], [], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], [], [], [], [], ["consonant_syllable", "broken_cluster"], ["consonant_syllable", "broken_cluster"], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], [], [], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], ["consonant_syllable"], [], ["consonant_syllable"], ["consonant_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], ["vowel_syllable"], [], ["vowel_syllable"], ["vowel_syllable"], ["broken_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], ["standalone_cluster"], [], ["standalone_cluster"], ["standalone_cluster"], [], ["consonant_syllable"], ["vowel_syllable"], ["standalone_cluster"]];
var indicMachine = {
  stateTable: stateTable,
  accepting: accepting,
  tags: tags
};
var categories = ["O", "IND", "S", "GB", "B", "FM", "CGJ", "VMAbv", "VMPst", "VAbv", "VPst", "CMBlw", "VPre", "VBlw", "H", "VMBlw", "CMAbv", "MBlw", "CS", "R", "SUB", "MPst", "MPre", "FAbv", "FPst", "FBlw", "null", "SMAbv", "SMBlw", "VMPre", "ZWNJ", "ZWJ", "WJ", "M", "VS", "N", "HN", "MAbv"];
var decompositions = {
  "2507": [2503, 2494],
  "2508": [2503, 2519],
  "2888": [2887, 2902],
  "2891": [2887, 2878],
  "2892": [2887, 2903],
  "3018": [3014, 3006],
  "3019": [3015, 3006],
  "3020": [3014, 3031],
  "3144": [3142, 3158],
  "3264": [3263, 3285],
  "3271": [3270, 3285],
  "3272": [3270, 3286],
  "3274": [3270, 3266],
  "3275": [3270, 3266, 3285],
  "3402": [3398, 3390],
  "3403": [3399, 3390],
  "3404": [3398, 3415],
  "3546": [3545, 3530],
  "3548": [3545, 3535],
  "3549": [3545, 3535, 3530],
  "3550": [3545, 3551],
  "3635": [3661, 3634],
  "3763": [3789, 3762],
  "3955": [3953, 3954],
  "3957": [3953, 3956],
  "3958": [4018, 3968],
  "3959": [4018, 3953, 3968],
  "3960": [4019, 3968],
  "3961": [4019, 3953, 3968],
  "3969": [3953, 3968],
  "6971": [6970, 6965],
  "6973": [6972, 6965],
  "6976": [6974, 6965],
  "6977": [6975, 6965],
  "6979": [6978, 6965],
  "69934": [69937, 69927],
  "69935": [69938, 69927],
  "70475": [70471, 70462],
  "70476": [70471, 70487],
  "70843": [70841, 70842],
  "70844": [70841, 70832],
  "70846": [70841, 70845],
  "71098": [71096, 71087],
  "71099": [71097, 71087]
};
var stateTable$1 = [[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [2, 2, 3, 4, 4, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 17, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 2, 0, 0, 24, 0, 25], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 26, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 28, 0, 0, 0, 0, 0, 27, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 39, 0, 0, 47], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 7, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 0, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 9, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 0, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 9, 0, 0, 12, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 18, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 0, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 4, 4, 5, 0, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 48, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 49, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 16, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 21, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 21, 22, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 0, 0, 0, 0, 0, 0, 14, 0, 0, 0, 0, 0, 0, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 50, 0, 51, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 0, 11, 12, 0, 14, 0, 16, 0, 0, 0, 11, 0, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 27, 28, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 28, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 31, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 0, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 33, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 0, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 33, 0, 0, 36, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 41, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 0, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 52, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 53, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 40, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 44, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 44, 45, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 0, 0, 0, 0, 0, 0, 38, 0, 0, 0, 0, 0, 0, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 0, 35, 36, 0, 38, 0, 40, 0, 0, 0, 35, 0, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 0], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 0, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 5, 0, 6, 7, 8, 9, 48, 11, 12, 13, 14, 48, 16, 0, 0, 18, 11, 19, 20, 21, 22, 0, 0, 0, 23, 0, 0, 0, 0, 0, 0, 0, 25], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 51, 0], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 54, 0, 0], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 0, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 29, 0, 30, 31, 32, 33, 52, 35, 36, 37, 38, 52, 40, 0, 0, 41, 35, 42, 43, 44, 45, 0, 0, 0, 46, 0, 0, 0, 0, 0, 0, 0, 47], [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 50, 0, 51, 0]];
var accepting$1 = [false, true, true, true, true, true, true, true, true, true, true, true, true, false, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true, true];
var tags$1 = [[], ["broken_cluster"], ["independent_cluster"], ["symbol_cluster"], ["standard_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], [], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["broken_cluster"], ["numeral_cluster"], ["broken_cluster"], ["independent_cluster"], ["symbol_cluster"], ["symbol_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["virama_terminated_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["standard_cluster"], ["broken_cluster"], ["broken_cluster"], ["numeral_cluster"], ["number_joiner_terminated_cluster"], ["standard_cluster"], ["standard_cluster"], ["numeral_cluster"]];
var useData = {
  categories: categories,
  decompositions: decompositions,
  stateTable: stateTable$1,
  accepting: accepting$1,
  tags: tags$1
};

// Cateories used in the OpenType spec:
// https://www.microsoft.com/typography/otfntdev/devanot/shaping.aspx
var CATEGORIES = {
  X: 1 << 0,
  C: 1 << 1,
  V: 1 << 2,
  N: 1 << 3,
  H: 1 << 4,
  ZWNJ: 1 << 5,
  ZWJ: 1 << 6,
  M: 1 << 7,
  SM: 1 << 8,
  VD: 1 << 9,
  A: 1 << 10,
  Placeholder: 1 << 11,
  Dotted_Circle: 1 << 12,
  RS: 1 << 13,
  // Register Shifter, used in Khmer OT spec.
  Coeng: 1 << 14,
  // Khmer-style Virama.
  Repha: 1 << 15,
  // Atomically-encoded logical or visual repha.
  Ra: 1 << 16,
  CM: 1 << 17,
  // Consonant-Medial.
  Symbol: 1 << 18 // Avagraha, etc that take marks (SM,A,VD).
}; // Visual positions in a syllable from left to right.

var POSITIONS = {
  Start: 1 << 0,
  Ra_To_Become_Reph: 1 << 1,
  Pre_M: 1 << 2,
  Pre_C: 1 << 3,
  Base_C: 1 << 4,
  After_Main: 1 << 5,
  Above_C: 1 << 6,
  Before_Sub: 1 << 7,
  Below_C: 1 << 8,
  After_Sub: 1 << 9,
  Before_Post: 1 << 10,
  Post_C: 1 << 11,
  After_Post: 1 << 12,
  Final_C: 1 << 13,
  SMVD: 1 << 14,
  End: 1 << 15
};
var CONSONANT_FLAGS = CATEGORIES.C | CATEGORIES.Ra | CATEGORIES.CM | CATEGORIES.V | CATEGORIES.Placeholder | CATEGORIES.Dotted_Circle;
var JOINER_FLAGS = CATEGORIES.ZWJ | CATEGORIES.ZWNJ;
var HALANT_OR_COENG_FLAGS = CATEGORIES.H | CATEGORIES.Coeng;
var INDIC_CONFIGS = {
  Default: {
    hasOldSpec: false,
    virama: 0,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Devanagari: {
    hasOldSpec: true,
    virama: 0x094D,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Bengali: {
    hasOldSpec: true,
    virama: 0x09CD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Sub,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Gurmukhi: {
    hasOldSpec: true,
    virama: 0x0A4D,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Sub,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Gujarati: {
    hasOldSpec: true,
    virama: 0x0ACD,
    basePos: 'Last',
    rephPos: POSITIONS.Before_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Oriya: {
    hasOldSpec: true,
    virama: 0x0B4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Main,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Tamil: {
    hasOldSpec: true,
    virama: 0x0BCD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Implicit',
    blwfMode: 'Pre_And_Post'
  },
  Telugu: {
    hasOldSpec: true,
    virama: 0x0C4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Explicit',
    blwfMode: 'Post_Only'
  },
  Kannada: {
    hasOldSpec: true,
    virama: 0x0CCD,
    basePos: 'Last',
    rephPos: POSITIONS.After_Post,
    rephMode: 'Implicit',
    blwfMode: 'Post_Only'
  },
  Malayalam: {
    hasOldSpec: true,
    virama: 0x0D4D,
    basePos: 'Last',
    rephPos: POSITIONS.After_Main,
    rephMode: 'Log_Repha',
    blwfMode: 'Pre_And_Post'
  },
  // Handled by UniversalShaper
  // Sinhala: {
  //   hasOldSpec: false,
  //   virama: 0x0DCA,
  //   basePos: 'Last_Sinhala',
  //   rephPos: POSITIONS.After_Main,
  //   rephMode: 'Explicit',
  //   blwfMode: 'Pre_And_Post'
  // },
  Khmer: {
    hasOldSpec: false,
    virama: 0x17D2,
    basePos: 'First',
    rephPos: POSITIONS.Ra_To_Become_Reph,
    rephMode: 'Vis_Repha',
    blwfMode: 'Pre_And_Post'
  }
}; // Additional decompositions that aren't in Unicode

var INDIC_DECOMPOSITIONS = {
  // Khmer
  0x17BE: [0x17C1, 0x17BE],
  0x17BF: [0x17C1, 0x17BF],
  0x17C0: [0x17C1, 0x17C0],
  0x17C4: [0x17C1, 0x17C4],
  0x17C5: [0x17C1, 0x17C5]
};
var decompositions$1 = useData.decompositions;
var trie$1 = new UnicodeTrie(Buffer("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","base64"));
var stateMachine = new StateMachine(indicMachine);
/**
 * The IndicShaper supports indic scripts e.g. Devanagari, Kannada, etc.
 * Based on code from Harfbuzz: https://github.com/behdad/harfbuzz/blob/master/src/hb-ot-shape-complex-indic.cc
 */
var IndicShaper = /*#__PURE__*/function (_DefaultShaper3) {
  function IndicShaper() {
    return _DefaultShaper3.apply(this, arguments) || this;
  }
  _inheritsLoose(IndicShaper, _DefaultShaper3);
  IndicShaper.planFeatures = function planFeatures(plan) {
    plan.addStage(setupSyllables);
    plan.addStage(['locl', 'ccmp']);
    plan.addStage(initialReordering);
    plan.addStage('nukt');
    plan.addStage('akhn');
    plan.addStage('rphf', false);
    plan.addStage('rkrf');
    plan.addStage('pref', false);
    plan.addStage('blwf', false);
    plan.addStage('abvf', false);
    plan.addStage('half', false);
    plan.addStage('pstf', false);
    plan.addStage('vatu');
    plan.addStage('cjct');
    plan.addStage('cfar', false);
    plan.addStage(finalReordering);
    plan.addStage({
      local: ['init'],
      global: ['pres', 'abvs', 'blws', 'psts', 'haln', 'dist', 'abvm', 'blwm', 'calt', 'clig']
    }); // Setup the indic config for the selected script

    plan.unicodeScript = fromOpenType(plan.script);
    plan.indicConfig = INDIC_CONFIGS[plan.unicodeScript] || INDIC_CONFIGS.Default;
    plan.isOldSpec = plan.indicConfig.hasOldSpec && plan.script[plan.script.length - 1] !== '2'; // TODO: turn off kern (Khmer) and liga features.
  };
  IndicShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var _loop = function _loop(i) {
      var codepoint = glyphs[i].codePoints[0];
      var d = INDIC_DECOMPOSITIONS[codepoint] || decompositions$1[codepoint];
      if (d) {
        var decomposed = d.map(function (c) {
          var g = plan.font.glyphForCodePoint(c);
          return new GlyphInfo(plan.font, g.id, [c], glyphs[i].features);
        });
        glyphs.splice.apply(glyphs, [i, 1].concat(decomposed));
      }
    };

    // Decompose split matras
    // TODO: do this in a more general unicode normalizer
    for (var i = glyphs.length - 1; i >= 0; i--) {
      _loop(i);
    }
  };
  return IndicShaper;
}(DefaultShaper);
_defineProperty(IndicShaper, "zeroMarkWidths", 'NONE');
function indicCategory(glyph) {
  return trie$1.get(glyph.codePoints[0]) >> 8;
}
function indicPosition(glyph) {
  return 1 << (trie$1.get(glyph.codePoints[0]) & 0xff);
}
var IndicInfo = function IndicInfo(category, position, syllableType, syllable) {
  this.category = category;
  this.position = position;
  this.syllableType = syllableType;
  this.syllable = syllable;
};
function setupSyllables(font, glyphs) {
  var syllable = 0;
  var last = 0;
  for (var _iterator53 = _createForOfIteratorHelperLoose(stateMachine.match(glyphs.map(indicCategory))), _step53; !(_step53 = _iterator53()).done;) {
    var _step53$value = _step53.value,
      start = _step53$value[0],
      end = _step53$value[1],
      tags = _step53$value[2];
    if (start > last) {
      ++syllable;
      for (var i = last; i < start; i++) {
        glyphs[i].shaperInfo = new IndicInfo(CATEGORIES.X, POSITIONS.End, 'non_indic_cluster', syllable);
      }
    }
    ++syllable; // Create shaper info

    for (var _i = start; _i <= end; _i++) {
      glyphs[_i].shaperInfo = new IndicInfo(1 << indicCategory(glyphs[_i]), indicPosition(glyphs[_i]), tags[0], syllable);
    }
    last = end + 1;
  }
  if (last < glyphs.length) {
    ++syllable;
    for (var _i2 = last; _i2 < glyphs.length; _i2++) {
      glyphs[_i2].shaperInfo = new IndicInfo(CATEGORIES.X, POSITIONS.End, 'non_indic_cluster', syllable);
    }
  }
}
function isConsonant(glyph) {
  return glyph.shaperInfo.category & CONSONANT_FLAGS;
}
function isJoiner(glyph) {
  return glyph.shaperInfo.category & JOINER_FLAGS;
}
function isHalantOrCoeng(glyph) {
  return glyph.shaperInfo.category & HALANT_OR_COENG_FLAGS;
}
function wouldSubstitute(glyphs, feature) {
  for (var _iterator54 = _createForOfIteratorHelperLoose(glyphs), _step54; !(_step54 = _iterator54()).done;) {
    var _glyph$features;
    var glyph = _step54.value;
    glyph.features = (_glyph$features = {}, _glyph$features[feature] = true, _glyph$features);
  }
  var GSUB = glyphs[0]._font._layoutEngine.engine.GSUBProcessor;
  GSUB.applyFeatures([feature], glyphs);
  return glyphs.length === 1;
}
function consonantPosition(font, consonant, virama) {
  var glyphs = [virama, consonant, virama];
  if (wouldSubstitute(glyphs.slice(0, 2), 'blwf') || wouldSubstitute(glyphs.slice(1, 3), 'blwf')) {
    return POSITIONS.Below_C;
  } else if (wouldSubstitute(glyphs.slice(0, 2), 'pstf') || wouldSubstitute(glyphs.slice(1, 3), 'pstf')) {
    return POSITIONS.Post_C;
  } else if (wouldSubstitute(glyphs.slice(0, 2), 'pref') || wouldSubstitute(glyphs.slice(1, 3), 'pref')) {
    return POSITIONS.Post_C;
  }
  return POSITIONS.Base_C;
}
function initialReordering(font, glyphs, plan) {
  var indicConfig = plan.indicConfig;
  var features = font._layoutEngine.engine.GSUBProcessor.features;
  var dottedCircle = font.glyphForCodePoint(0x25cc).id;
  var virama = font.glyphForCodePoint(indicConfig.virama).id;
  if (virama) {
    var info = new GlyphInfo(font, virama, [indicConfig.virama]);
    for (var i = 0; i < glyphs.length; i++) {
      if (glyphs[i].shaperInfo.position === POSITIONS.Base_C) {
        glyphs[i].shaperInfo.position = consonantPosition(font, glyphs[i].copy(), info);
      }
    }
  }
  for (var start = 0, end = nextSyllable(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable(glyphs, start)) {
    var _glyphs$start$shaperI = glyphs[start].shaperInfo,
      category = _glyphs$start$shaperI.category,
      syllableType = _glyphs$start$shaperI.syllableType;
    if (syllableType === 'symbol_cluster' || syllableType === 'non_indic_cluster') {
      continue;
    }
    if (syllableType === 'broken_cluster' && dottedCircle) {
      var g = new GlyphInfo(font, dottedCircle, [0x25cc]);
      g.shaperInfo = new IndicInfo(1 << indicCategory(g), indicPosition(g), glyphs[start].shaperInfo.syllableType, glyphs[start].shaperInfo.syllable); // Insert after possible Repha.

      var _i3 = start;
      while (_i3 < end && glyphs[_i3].shaperInfo.category === CATEGORIES.Repha) {
        _i3++;
      }
      glyphs.splice(_i3++, 0, g);
      end++;
    } // 1. Find base consonant:
    //
    // The shaping engine finds the base consonant of the syllable, using the
    // following algorithm: starting from the end of the syllable, move backwards
    // until a consonant is found that does not have a below-base or post-base
    // form (post-base forms have to follow below-base forms), or that is not a
    // pre-base reordering Ra, or arrive at the first consonant. The consonant
    // stopped at will be the base.

    var base = end;
    var limit = start;
    var hasReph = false; // If the syllable starts with Ra + Halant (in a script that has Reph)
    // and has more than one consonant, Ra is excluded from candidates for
    // base consonants.

    if (indicConfig.rephPos !== POSITIONS.Ra_To_Become_Reph && features.rphf && start + 3 <= end && (indicConfig.rephMode === 'Implicit' && !isJoiner(glyphs[start + 2]) || indicConfig.rephMode === 'Explicit' && glyphs[start + 2].shaperInfo.category === CATEGORIES.ZWJ)) {
      // See if it matches the 'rphf' feature.
      var _g = [glyphs[start].copy(), glyphs[start + 1].copy(), glyphs[start + 2].copy()];
      if (wouldSubstitute(_g.slice(0, 2), 'rphf') || indicConfig.rephMode === 'Explicit' && wouldSubstitute(_g, 'rphf')) {
        limit += 2;
        while (limit < end && isJoiner(glyphs[limit])) {
          limit++;
        }
        base = start;
        hasReph = true;
      }
    } else if (indicConfig.rephMode === 'Log_Repha' && glyphs[start].shaperInfo.category === CATEGORIES.Repha) {
      limit++;
      while (limit < end && isJoiner(glyphs[limit])) {
        limit++;
      }
      base = start;
      hasReph = true;
    }
    switch (indicConfig.basePos) {
      case 'Last':
        {
          // starting from the end of the syllable, move backwards
          var _i4 = end;
          var seenBelow = false;
          do {
            var _info = glyphs[--_i4].shaperInfo; // until a consonant is found

            if (isConsonant(glyphs[_i4])) {
              // that does not have a below-base or post-base form
              // (post-base forms have to follow below-base forms),
              if (_info.position !== POSITIONS.Below_C && (_info.position !== POSITIONS.Post_C || seenBelow)) {
                base = _i4;
                break;
              } // or that is not a pre-base reordering Ra,
              //
              // IMPLEMENTATION NOTES:
              //
              // Our pre-base reordering Ra's are marked POS_POST_C, so will be skipped
              // by the logic above already.
              //
              // or arrive at the first consonant. The consonant stopped at will
              // be the base.

              if (_info.position === POSITIONS.Below_C) {
                seenBelow = true;
              }
              base = _i4;
            } else if (start < _i4 && _info.category === CATEGORIES.ZWJ && glyphs[_i4 - 1].shaperInfo.category === CATEGORIES.H) {
              // A ZWJ after a Halant stops the base search, and requests an explicit
              // half form.
              // A ZWJ before a Halant, requests a subjoined form instead, and hence
              // search continues.  This is particularly important for Bengali
              // sequence Ra,H,Ya that should form Ya-Phalaa by subjoining Ya.
              break;
            }
          } while (_i4 > limit);
          break;
        }
      case 'First':
        {
          // The first consonant is always the base.
          base = start; // Mark all subsequent consonants as below.

          for (var _i5 = base + 1; _i5 < end; _i5++) {
            if (isConsonant(glyphs[_i5])) {
              glyphs[_i5].shaperInfo.position = POSITIONS.Below_C;
            }
          }
        }
    } // If the syllable starts with Ra + Halant (in a script that has Reph)
    // and has more than one consonant, Ra is excluded from candidates for
    // base consonants.
    //
    //  Only do this for unforced Reph. (ie. not for Ra,H,ZWJ)

    if (hasReph && base === start && limit - base <= 2) {
      hasReph = false;
    } // 2. Decompose and reorder Matras:
    //
    // Each matra and any syllable modifier sign in the cluster are moved to the
    // appropriate position relative to the consonant(s) in the cluster. The
    // shaping engine decomposes two- or three-part matras into their constituent
    // parts before any repositioning. Matra characters are classified by which
    // consonant in a conjunct they have affinity for and are reordered to the
    // following positions:
    //
    //   o Before first half form in the syllable
    //   o After subjoined consonants
    //   o After post-form consonant
    //   o After main consonant (for above marks)
    //
    // IMPLEMENTATION NOTES:
    //
    // The normalize() routine has already decomposed matras for us, so we don't
    // need to worry about that.
    // 3.  Reorder marks to canonical order:
    //
    // Adjacent nukta and halant or nukta and vedic sign are always repositioned
    // if necessary, so that the nukta is first.
    //
    // IMPLEMENTATION NOTES:
    //
    // We don't need to do this: the normalize() routine already did this for us.
    // Reorder characters

    for (var _i6 = start; _i6 < base; _i6++) {
      var _info2 = glyphs[_i6].shaperInfo;
      _info2.position = Math.min(POSITIONS.Pre_C, _info2.position);
    }
    if (base < end) {
      glyphs[base].shaperInfo.position = POSITIONS.Base_C;
    } // Mark final consonants.  A final consonant is one appearing after a matra,
    // like in Khmer.

    for (var _i7 = base + 1; _i7 < end; _i7++) {
      if (glyphs[_i7].shaperInfo.category === CATEGORIES.M) {
        for (var j = _i7 + 1; j < end; j++) {
          if (isConsonant(glyphs[j])) {
            glyphs[j].shaperInfo.position = POSITIONS.Final_C;
            break;
          }
        }
        break;
      }
    } // Handle beginning Ra

    if (hasReph) {
      glyphs[start].shaperInfo.position = POSITIONS.Ra_To_Become_Reph;
    } // For old-style Indic script tags, move the first post-base Halant after
    // last consonant.
    //
    // Reports suggest that in some scripts Uniscribe does this only if there
    // is *not* a Halant after last consonant already (eg. Kannada), while it
    // does it unconditionally in other scripts (eg. Malayalam).  We don't
    // currently know about other scripts, so we single out Malayalam for now.
    //
    // Kannada test case:
    // U+0C9A,U+0CCD,U+0C9A,U+0CCD
    // With some versions of Lohit Kannada.
    // https://bugs.freedesktop.org/show_bug.cgi?id=59118
    //
    // Malayalam test case:
    // U+0D38,U+0D4D,U+0D31,U+0D4D,U+0D31,U+0D4D
    // With lohit-ttf-20121122/Lohit-Malayalam.ttf

    if (plan.isOldSpec) {
      var disallowDoubleHalants = plan.unicodeScript !== 'Malayalam';
      for (var _i8 = base + 1; _i8 < end; _i8++) {
        if (glyphs[_i8].shaperInfo.category === CATEGORIES.H) {
          var _j = void 0;
          for (_j = end - 1; _j > _i8; _j--) {
            if (isConsonant(glyphs[_j]) || disallowDoubleHalants && glyphs[_j].shaperInfo.category === CATEGORIES.H) {
              break;
            }
          }
          if (glyphs[_j].shaperInfo.category !== CATEGORIES.H && _j > _i8) {
            // Move Halant to after last consonant.
            var t = glyphs[_i8];
            glyphs.splice.apply(glyphs, [_i8, 0].concat(glyphs.splice(_i8 + 1, _j - _i8)));
            glyphs[_j] = t;
          }
          break;
        }
      }
    } // Attach misc marks to previous char to move with them.

    var lastPos = POSITIONS.Start;
    for (var _i9 = start; _i9 < end; _i9++) {
      var _info3 = glyphs[_i9].shaperInfo;
      if (_info3.category & (JOINER_FLAGS | CATEGORIES.N | CATEGORIES.RS | CATEGORIES.CM | HALANT_OR_COENG_FLAGS & _info3.category)) {
        _info3.position = lastPos;
        if (_info3.category === CATEGORIES.H && _info3.position === POSITIONS.Pre_M) {
          // Uniscribe doesn't move the Halant with Left Matra.
          // TEST: U+092B,U+093F,U+094DE
          // We follow.  This is important for the Sinhala
          // U+0DDA split matra since it decomposes to U+0DD9,U+0DCA
          // where U+0DD9 is a left matra and U+0DCA is the virama.
          // We don't want to move the virama with the left matra.
          // TEST: U+0D9A,U+0DDA
          for (var _j2 = _i9; _j2 > start; _j2--) {
            if (glyphs[_j2 - 1].shaperInfo.position !== POSITIONS.Pre_M) {
              _info3.position = glyphs[_j2 - 1].shaperInfo.position;
              break;
            }
          }
        }
      } else if (_info3.position !== POSITIONS.SMVD) {
        lastPos = _info3.position;
      }
    } // For post-base consonants let them own anything before them
    // since the last consonant or matra.

    var last = base;
    for (var _i10 = base + 1; _i10 < end; _i10++) {
      if (isConsonant(glyphs[_i10])) {
        for (var _j3 = last + 1; _j3 < _i10; _j3++) {
          if (glyphs[_j3].shaperInfo.position < POSITIONS.SMVD) {
            glyphs[_j3].shaperInfo.position = glyphs[_i10].shaperInfo.position;
          }
        }
        last = _i10;
      } else if (glyphs[_i10].shaperInfo.category === CATEGORIES.M) {
        last = _i10;
      }
    }
    var arr = glyphs.slice(start, end);
    arr.sort(function (a, b) {
      return a.shaperInfo.position - b.shaperInfo.position;
    });
    glyphs.splice.apply(glyphs, [start, arr.length].concat(arr)); // Find base again

    for (var _i11 = start; _i11 < end; _i11++) {
      if (glyphs[_i11].shaperInfo.position === POSITIONS.Base_C) {
        base = _i11;
        break;
      }
    } // Setup features now
    // Reph

    for (var _i12 = start; _i12 < end && glyphs[_i12].shaperInfo.position === POSITIONS.Ra_To_Become_Reph; _i12++) {
      glyphs[_i12].features.rphf = true;
    } // Pre-base

    var blwf = !plan.isOldSpec && indicConfig.blwfMode === 'Pre_And_Post';
    for (var _i13 = start; _i13 < base; _i13++) {
      glyphs[_i13].features.half = true;
      if (blwf) {
        glyphs[_i13].features.blwf = true;
      }
    } // Post-base

    for (var _i14 = base + 1; _i14 < end; _i14++) {
      glyphs[_i14].features.abvf = true;
      glyphs[_i14].features.pstf = true;
      glyphs[_i14].features.blwf = true;
    }
    if (plan.isOldSpec && plan.unicodeScript === 'Devanagari') {
      // Old-spec eye-lash Ra needs special handling.  From the
      // spec:
      //
      // "The feature 'below-base form' is applied to consonants
      // having below-base forms and following the base consonant.
      // The exception is vattu, which may appear below half forms
      // as well as below the base glyph. The feature 'below-base
      // form' will be applied to all such occurrences of Ra as well."
      //
      // Test case: U+0924,U+094D,U+0930,U+094d,U+0915
      // with Sanskrit 2003 font.
      //
      // However, note that Ra,Halant,ZWJ is the correct way to
      // request eyelash form of Ra, so we wouldbn't inhibit it
      // in that sequence.
      //
      // Test case: U+0924,U+094D,U+0930,U+094d,U+200D,U+0915
      for (var _i15 = start; _i15 + 1 < base; _i15++) {
        if (glyphs[_i15].shaperInfo.category === CATEGORIES.Ra && glyphs[_i15 + 1].shaperInfo.category === CATEGORIES.H && (_i15 + 1 === base || glyphs[_i15 + 2].shaperInfo.category === CATEGORIES.ZWJ)) {
          glyphs[_i15].features.blwf = true;
          glyphs[_i15 + 1].features.blwf = true;
        }
      }
    }
    var prefLen = 2;
    if (features.pref && base + prefLen < end) {
      // Find a Halant,Ra sequence and mark it for pre-base reordering processing.
      for (var _i16 = base + 1; _i16 + prefLen - 1 < end; _i16++) {
        var _g2 = [glyphs[_i16].copy(), glyphs[_i16 + 1].copy()];
        if (wouldSubstitute(_g2, 'pref')) {
          for (var _j4 = 0; _j4 < prefLen; _j4++) {
            glyphs[_i16++].features.pref = true;
          } // Mark the subsequent stuff with 'cfar'.  Used in Khmer.
          // Read the feature spec.
          // This allows distinguishing the following cases with MS Khmer fonts:
          // U+1784,U+17D2,U+179A,U+17D2,U+1782
          // U+1784,U+17D2,U+1782,U+17D2,U+179A

          if (features.cfar) {
            for (; _i16 < end; _i16++) {
              glyphs[_i16].features.cfar = true;
            }
          }
          break;
        }
      }
    } // Apply ZWJ/ZWNJ effects

    for (var _i17 = start + 1; _i17 < end; _i17++) {
      if (isJoiner(glyphs[_i17])) {
        var nonJoiner = glyphs[_i17].shaperInfo.category === CATEGORIES.ZWNJ;
        var _j5 = _i17;
        do {
          _j5--; // ZWJ/ZWNJ should disable CJCT.  They do that by simply
          // being there, since we don't skip them for the CJCT
          // feature (ie. F_MANUAL_ZWJ)
          // A ZWNJ disables HALF.

          if (nonJoiner) {
            delete glyphs[_j5].features.half;
          }
        } while (_j5 > start && !isConsonant(glyphs[_j5]));
      }
    }
  }
}
function finalReordering(font, glyphs, plan) {
  var indicConfig = plan.indicConfig;
  var features = font._layoutEngine.engine.GSUBProcessor.features;
  for (var start = 0, end = nextSyllable(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable(glyphs, start)) {
    // 4. Final reordering:
    //
    // After the localized forms and basic shaping forms GSUB features have been
    // applied (see below), the shaping engine performs some final glyph
    // reordering before applying all the remaining font features to the entire
    // cluster.
    var tryPref = !!features.pref; // Find base again

    var base = start;
    for (; base < end; base++) {
      if (glyphs[base].shaperInfo.position >= POSITIONS.Base_C) {
        if (tryPref && base + 1 < end) {
          for (var i = base + 1; i < end; i++) {
            if (glyphs[i].features.pref) {
              if (!(glyphs[i].substituted && glyphs[i].isLigated && !glyphs[i].isMultiplied)) {
                // Ok, this was a 'pref' candidate but didn't form any.
                // Base is around here...
                base = i;
                while (base < end && isHalantOrCoeng(glyphs[base])) {
                  base++;
                }
                glyphs[base].shaperInfo.position = POSITIONS.BASE_C;
                tryPref = false;
              }
              break;
            }
          }
        } // For Malayalam, skip over unformed below- (but NOT post-) forms.

        if (plan.unicodeScript === 'Malayalam') {
          for (var _i18 = base + 1; _i18 < end; _i18++) {
            while (_i18 < end && isJoiner(glyphs[_i18])) {
              _i18++;
            }
            if (_i18 === end || !isHalantOrCoeng(glyphs[_i18])) {
              break;
            }
            _i18++; // Skip halant.

            while (_i18 < end && isJoiner(glyphs[_i18])) {
              _i18++;
            }
            if (_i18 < end && isConsonant(glyphs[_i18]) && glyphs[_i18].shaperInfo.position === POSITIONS.Below_C) {
              base = _i18;
              glyphs[base].shaperInfo.position = POSITIONS.Base_C;
            }
          }
        }
        if (start < base && glyphs[base].shaperInfo.position > POSITIONS.Base_C) {
          base--;
        }
        break;
      }
    }
    if (base === end && start < base && glyphs[base - 1].shaperInfo.category === CATEGORIES.ZWJ) {
      base--;
    }
    if (base < end) {
      while (start < base && glyphs[base].shaperInfo.category & (CATEGORIES.N | HALANT_OR_COENG_FLAGS)) {
        base--;
      }
    } // o Reorder matras:
    //
    // If a pre-base matra character had been reordered before applying basic
    // features, the glyph can be moved closer to the main consonant based on
    // whether half-forms had been formed. Actual position for the matra is
    // defined as “after last standalone halant glyph, after initial matra
    // position and before the main consonant”. If ZWJ or ZWNJ follow this
    // halant, position is moved after it.
    //

    if (start + 1 < end && start < base) {
      // Otherwise there can't be any pre-base matra characters.
      // If we lost track of base, alas, position before last thingy.
      var newPos = base === end ? base - 2 : base - 1; // Malayalam / Tamil do not have "half" forms or explicit virama forms.
      // The glyphs formed by 'half' are Chillus or ligated explicit viramas.
      // We want to position matra after them.

      if (plan.unicodeScript !== 'Malayalam' && plan.unicodeScript !== 'Tamil') {
        while (newPos > start && !(glyphs[newPos].shaperInfo.category & (CATEGORIES.M | HALANT_OR_COENG_FLAGS))) {
          newPos--;
        } // If we found no Halant we are done.
        // Otherwise only proceed if the Halant does
        // not belong to the Matra itself!

        if (isHalantOrCoeng(glyphs[newPos]) && glyphs[newPos].shaperInfo.position !== POSITIONS.Pre_M) {
          // If ZWJ or ZWNJ follow this halant, position is moved after it.
          if (newPos + 1 < end && isJoiner(glyphs[newPos + 1])) {
            newPos++;
          }
        } else {
          newPos = start; // No move.
        }
      }
      if (start < newPos && glyphs[newPos].shaperInfo.position !== POSITIONS.Pre_M) {
        // Now go see if there's actually any matras...
        for (var _i19 = newPos; _i19 > start; _i19--) {
          if (glyphs[_i19 - 1].shaperInfo.position === POSITIONS.Pre_M) {
            var oldPos = _i19 - 1;
            if (oldPos < base && base <= newPos) {
              // Shouldn't actually happen.
              base--;
            }
            var tmp = glyphs[oldPos];
            glyphs.splice.apply(glyphs, [oldPos, 0].concat(glyphs.splice(oldPos + 1, newPos - oldPos)));
            glyphs[newPos] = tmp;
            newPos--;
          }
        }
      }
    } // o Reorder reph:
    //
    // Reph’s original position is always at the beginning of the syllable,
    // (i.e. it is not reordered at the character reordering stage). However,
    // it will be reordered according to the basic-forms shaping results.
    // Possible positions for reph, depending on the script, are; after main,
    // before post-base consonant forms, and after post-base consonant forms.
    // Two cases:
    //
    // - If repha is encoded as a sequence of characters (Ra,H or Ra,H,ZWJ), then
    //   we should only move it if the sequence ligated to the repha form.
    //
    // - If repha is encoded separately and in the logical position, we should only
    //   move it if it did NOT ligate.  If it ligated, it's probably the font trying
    //   to make it work without the reordering.

    if (start + 1 < end && glyphs[start].shaperInfo.position === POSITIONS.Ra_To_Become_Reph && glyphs[start].shaperInfo.category === CATEGORIES.Repha !== (glyphs[start].isLigated && !glyphs[start].isMultiplied)) {
      var newRephPos = void 0;
      var rephPos = indicConfig.rephPos;
      var found = false; // 1. If reph should be positioned after post-base consonant forms,
      //    proceed to step 5.

      if (rephPos !== POSITIONS.After_Post) {
        //  2. If the reph repositioning class is not after post-base: target
        //     position is after the first explicit halant glyph between the
        //     first post-reph consonant and last main consonant. If ZWJ or ZWNJ
        //     are following this halant, position is moved after it. If such
        //     position is found, this is the target position. Otherwise,
        //     proceed to the next step.
        //
        //     Note: in old-implementation fonts, where classifications were
        //     fixed in shaping engine, there was no case where reph position
        //     will be found on this step.
        newRephPos = start + 1;
        while (newRephPos < base && !isHalantOrCoeng(glyphs[newRephPos])) {
          newRephPos++;
        }
        if (newRephPos < base && isHalantOrCoeng(glyphs[newRephPos])) {
          // ->If ZWJ or ZWNJ are following this halant, position is moved after it.
          if (newRephPos + 1 < base && isJoiner(glyphs[newRephPos + 1])) {
            newRephPos++;
          }
          found = true;
        } // 3. If reph should be repositioned after the main consonant: find the
        //    first consonant not ligated with main, or find the first
        //    consonant that is not a potential pre-base reordering Ra.

        if (!found && rephPos === POSITIONS.After_Main) {
          newRephPos = base;
          while (newRephPos + 1 < end && glyphs[newRephPos + 1].shaperInfo.position <= POSITIONS.After_Main) {
            newRephPos++;
          }
          found = newRephPos < end;
        } // 4. If reph should be positioned before post-base consonant, find
        //    first post-base classified consonant not ligated with main. If no
        //    consonant is found, the target position should be before the
        //    first matra, syllable modifier sign or vedic sign.
        //
        // This is our take on what step 4 is trying to say (and failing, BADLY).

        if (!found && rephPos === POSITIONS.After_Sub) {
          newRephPos = base;
          while (newRephPos + 1 < end && !(glyphs[newRephPos + 1].shaperInfo.position & (POSITIONS.Post_C | POSITIONS.After_Post | POSITIONS.SMVD))) {
            newRephPos++;
          }
          found = newRephPos < end;
        }
      } //  5. If no consonant is found in steps 3 or 4, move reph to a position
      //     immediately before the first post-base matra, syllable modifier
      //     sign or vedic sign that has a reordering class after the intended
      //     reph position. For example, if the reordering position for reph
      //     is post-main, it will skip above-base matras that also have a
      //     post-main position.

      if (!found) {
        // Copied from step 2.
        newRephPos = start + 1;
        while (newRephPos < base && !isHalantOrCoeng(glyphs[newRephPos])) {
          newRephPos++;
        }
        if (newRephPos < base && isHalantOrCoeng(glyphs[newRephPos])) {
          // ->If ZWJ or ZWNJ are following this halant, position is moved after it.
          if (newRephPos + 1 < base && isJoiner(glyphs[newRephPos + 1])) {
            newRephPos++;
          }
          found = true;
        }
      } // 6. Otherwise, reorder reph to the end of the syllable.

      if (!found) {
        newRephPos = end - 1;
        while (newRephPos > start && glyphs[newRephPos].shaperInfo.position === POSITIONS.SMVD) {
          newRephPos--;
        } // If the Reph is to be ending up after a Matra,Halant sequence,
        // position it before that Halant so it can interact with the Matra.
        // However, if it's a plain Consonant,Halant we shouldn't do that.
        // Uniscribe doesn't do this.
        // TEST: U+0930,U+094D,U+0915,U+094B,U+094D

        if (isHalantOrCoeng(glyphs[newRephPos])) {
          for (var _i20 = base + 1; _i20 < newRephPos; _i20++) {
            if (glyphs[_i20].shaperInfo.category === CATEGORIES.M) {
              newRephPos--;
            }
          }
        }
      }
      var reph = glyphs[start];
      glyphs.splice.apply(glyphs, [start, 0].concat(glyphs.splice(start + 1, newRephPos - start)));
      glyphs[newRephPos] = reph;
      if (start < base && base <= newRephPos) {
        base--;
      }
    } // o Reorder pre-base reordering consonants:
    //
    // If a pre-base reordering consonant is found, reorder it according to
    // the following rules:

    if (tryPref && base + 1 < end) {
      for (var _i21 = base + 1; _i21 < end; _i21++) {
        if (glyphs[_i21].features.pref) {
          // 1. Only reorder a glyph produced by substitution during application
          //    of the <pref> feature. (Note that a font may shape a Ra consonant with
          //    the feature generally but block it in certain contexts.)
          // Note: We just check that something got substituted.  We don't check that
          // the <pref> feature actually did it...
          //
          // Reorder pref only if it ligated.
          if (glyphs[_i21].isLigated && !glyphs[_i21].isMultiplied) {
            // 2. Try to find a target position the same way as for pre-base matra.
            //    If it is found, reorder pre-base consonant glyph.
            //
            // 3. If position is not found, reorder immediately before main
            //    consonant.
            var _newPos = base; // Malayalam / Tamil do not have "half" forms or explicit virama forms.
            // The glyphs formed by 'half' are Chillus or ligated explicit viramas.
            // We want to position matra after them.

            if (plan.unicodeScript !== 'Malayalam' && plan.unicodeScript !== 'Tamil') {
              while (_newPos > start && !(glyphs[_newPos - 1].shaperInfo.category & (CATEGORIES.M | HALANT_OR_COENG_FLAGS))) {
                _newPos--;
              } // In Khmer coeng model, a H,Ra can go *after* matras.  If it goes after a
              // split matra, it should be reordered to *before* the left part of such matra.

              if (_newPos > start && glyphs[_newPos - 1].shaperInfo.category === CATEGORIES.M) {
                var _oldPos2 = _i21;
                for (var j = base + 1; j < _oldPos2; j++) {
                  if (glyphs[j].shaperInfo.category === CATEGORIES.M) {
                    _newPos--;
                    break;
                  }
                }
              }
            }
            if (_newPos > start && isHalantOrCoeng(glyphs[_newPos - 1])) {
              // -> If ZWJ or ZWNJ follow this halant, position is moved after it.
              if (_newPos < end && isJoiner(glyphs[_newPos])) {
                _newPos++;
              }
            }
            var _oldPos = _i21;
            var _tmp = glyphs[_oldPos];
            glyphs.splice.apply(glyphs, [_newPos + 1, 0].concat(glyphs.splice(_newPos, _oldPos - _newPos)));
            glyphs[_newPos] = _tmp;
            if (_newPos <= base && base < _oldPos) {
              base++;
            }
          }
          break;
        }
      }
    } // Apply 'init' to the Left Matra if it's a word start.

    if (glyphs[start].shaperInfo.position === POSITIONS.Pre_M && (!start || !/Cf|Mn/.test(unicode.getCategory(glyphs[start - 1].codePoints[0])))) {
      glyphs[start].features.init = true;
    }
  }
}
function nextSyllable(glyphs, start) {
  if (start >= glyphs.length) return start;
  var syllable = glyphs[start].shaperInfo.syllable;
  while (++start < glyphs.length && glyphs[start].shaperInfo.syllable === syllable) {}
  return start;
}
var categories$1 = useData.categories,
  decompositions$2 = useData.decompositions;
var trie$2 = new UnicodeTrie(Buffer("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","base64"));
var stateMachine$1 = new StateMachine(useData);
/**
 * This shaper is an implementation of the Universal Shaping Engine, which
 * uses Unicode data to shape a number of scripts without a dedicated shaping engine.
 * See https://www.microsoft.com/typography/OpenTypeDev/USE/intro.htm.
 */
var UniversalShaper = /*#__PURE__*/function (_DefaultShaper4) {
  function UniversalShaper() {
    return _DefaultShaper4.apply(this, arguments) || this;
  }
  _inheritsLoose(UniversalShaper, _DefaultShaper4);
  UniversalShaper.planFeatures = function planFeatures(plan) {
    plan.addStage(setupSyllables$1); // Default glyph pre-processing group

    plan.addStage(['locl', 'ccmp', 'nukt', 'akhn']); // Reordering group

    plan.addStage(clearSubstitutionFlags);
    plan.addStage(['rphf'], false);
    plan.addStage(recordRphf);
    plan.addStage(clearSubstitutionFlags);
    plan.addStage(['pref']);
    plan.addStage(recordPref); // Orthographic unit shaping group

    plan.addStage(['rkrf', 'abvf', 'blwf', 'half', 'pstf', 'vatu', 'cjct']);
    plan.addStage(reorder); // Topographical features
    // Scripts that need this are handled by the Arabic shaper, not implemented here for now.
    // plan.addStage(['isol', 'init', 'medi', 'fina', 'med2', 'fin2', 'fin3'], false);
    // Standard topographic presentation and positional feature application

    plan.addStage(['abvs', 'blws', 'pres', 'psts', 'dist', 'abvm', 'blwm']);
  };
  UniversalShaper.assignFeatures = function assignFeatures(plan, glyphs) {
    var _loop = function _loop(i) {
      var codepoint = glyphs[i].codePoints[0];
      if (decompositions$2[codepoint]) {
        var decomposed = decompositions$2[codepoint].map(function (c) {
          var g = plan.font.glyphForCodePoint(c);
          return new GlyphInfo(plan.font, g.id, [c], glyphs[i].features);
        });
        glyphs.splice.apply(glyphs, [i, 1].concat(decomposed));
      }
    };

    // Decompose split vowels
    // TODO: do this in a more general unicode normalizer
    for (var i = glyphs.length - 1; i >= 0; i--) {
      _loop(i);
    }
  };
  return UniversalShaper;
}(DefaultShaper);
_defineProperty(UniversalShaper, "zeroMarkWidths", 'BEFORE_GPOS');
function useCategory(glyph) {
  return trie$2.get(glyph.codePoints[0]);
}
var USEInfo = function USEInfo(category, syllableType, syllable) {
  this.category = category;
  this.syllableType = syllableType;
  this.syllable = syllable;
};
function setupSyllables$1(font, glyphs) {
  var syllable = 0;
  for (var _iterator55 = _createForOfIteratorHelperLoose(stateMachine$1.match(glyphs.map(useCategory))), _step55; !(_step55 = _iterator55()).done;) {
    var _step55$value = _step55.value,
      start = _step55$value[0],
      end = _step55$value[1],
      tags = _step55$value[2];
    ++syllable; // Create shaper info

    for (var i = start; i <= end; i++) {
      glyphs[i].shaperInfo = new USEInfo(categories$1[useCategory(glyphs[i])], tags[0], syllable);
    } // Assign rphf feature

    var limit = glyphs[start].shaperInfo.category === 'R' ? 1 : Math.min(3, end - start);
    for (var _i = start; _i < start + limit; _i++) {
      glyphs[_i].features.rphf = true;
    }
  }
}
function clearSubstitutionFlags(font, glyphs) {
  for (var _iterator56 = _createForOfIteratorHelperLoose(glyphs), _step56; !(_step56 = _iterator56()).done;) {
    var glyph = _step56.value;
    glyph.substituted = false;
  }
}
function recordRphf(font, glyphs) {
  for (var _iterator57 = _createForOfIteratorHelperLoose(glyphs), _step57; !(_step57 = _iterator57()).done;) {
    var glyph = _step57.value;
    if (glyph.substituted && glyph.features.rphf) {
      // Mark a substituted repha.
      glyph.shaperInfo.category = 'R';
    }
  }
}
function recordPref(font, glyphs) {
  for (var _iterator58 = _createForOfIteratorHelperLoose(glyphs), _step58; !(_step58 = _iterator58()).done;) {
    var glyph = _step58.value;
    if (glyph.substituted) {
      // Mark a substituted pref as VPre, as they behave the same way.
      glyph.shaperInfo.category = 'VPre';
    }
  }
}
function reorder(font, glyphs) {
  var dottedCircle = font.glyphForCodePoint(0x25cc).id;
  for (var start = 0, end = nextSyllable$1(glyphs, 0); start < glyphs.length; start = end, end = nextSyllable$1(glyphs, start)) {
    var i = void 0,
      j = void 0;
    var info = glyphs[start].shaperInfo;
    var type = info.syllableType; // Only a few syllable types need reordering.

    if (type !== 'virama_terminated_cluster' && type !== 'standard_cluster' && type !== 'broken_cluster') {
      continue;
    } // Insert a dotted circle glyph in broken clusters.

    if (type === 'broken_cluster' && dottedCircle) {
      var g = new GlyphInfo(font, dottedCircle, [0x25cc]);
      g.shaperInfo = info; // Insert after possible Repha.

      for (i = start; i < end && glyphs[i].shaperInfo.category === 'R'; i++) {}
      glyphs.splice(++i, 0, g);
      end++;
    } // Move things forward.

    if (info.category === 'R' && end - start > 1) {
      // Got a repha. Reorder it to after first base, before first halant.
      for (i = start + 1; i < end; i++) {
        info = glyphs[i].shaperInfo;
        if (isBase(info) || isHalant(glyphs[i])) {
          // If we hit a halant, move before it; otherwise it's a base: move to it's
          // place, and shift things in between backward.
          if (isHalant(glyphs[i])) {
            i--;
          }
          glyphs.splice.apply(glyphs, [start, 0].concat(glyphs.splice(start + 1, i - start), [glyphs[i]]));
          break;
        }
      }
    } // Move things back.

    for (i = start, j = end; i < end; i++) {
      info = glyphs[i].shaperInfo;
      if (isBase(info) || isHalant(glyphs[i])) {
        // If we hit a halant, move after it; otherwise it's a base: move to it's
        // place, and shift things in between backward.
        j = isHalant(glyphs[i]) ? i + 1 : i;
      } else if ((info.category === 'VPre' || info.category === 'VMPre') && j < i) {
        glyphs.splice.apply(glyphs, [j, 1, glyphs[i]].concat(glyphs.splice(j, i - j)));
      }
    }
  }
}
function nextSyllable$1(glyphs, start) {
  if (start >= glyphs.length) return start;
  var syllable = glyphs[start].shaperInfo.syllable;
  while (++start < glyphs.length && glyphs[start].shaperInfo.syllable === syllable) {}
  return start;
}
function isHalant(glyph) {
  return glyph.shaperInfo.category === 'H' && !glyph.isLigated;
}
function isBase(info) {
  return info.category === 'B' || info.category === 'GB';
}
var SHAPERS = {
  arab: ArabicShaper,
  // Arabic
  mong: ArabicShaper,
  // Mongolian
  syrc: ArabicShaper,
  // Syriac
  'nko ': ArabicShaper,
  // N'Ko
  phag: ArabicShaper,
  // Phags Pa
  mand: ArabicShaper,
  // Mandaic
  mani: ArabicShaper,
  // Manichaean
  phlp: ArabicShaper,
  // Psalter Pahlavi
  hang: HangulShaper,
  // Hangul
  bng2: IndicShaper,
  // Bengali
  beng: IndicShaper,
  // Bengali
  dev2: IndicShaper,
  // Devanagari
  deva: IndicShaper,
  // Devanagari
  gjr2: IndicShaper,
  // Gujarati
  gujr: IndicShaper,
  // Gujarati
  guru: IndicShaper,
  // Gurmukhi
  gur2: IndicShaper,
  // Gurmukhi
  knda: IndicShaper,
  // Kannada
  knd2: IndicShaper,
  // Kannada
  mlm2: IndicShaper,
  // Malayalam
  mlym: IndicShaper,
  // Malayalam
  ory2: IndicShaper,
  // Oriya
  orya: IndicShaper,
  // Oriya
  taml: IndicShaper,
  // Tamil
  tml2: IndicShaper,
  // Tamil
  telu: IndicShaper,
  // Telugu
  tel2: IndicShaper,
  // Telugu
  khmr: IndicShaper,
  // Khmer
  bali: UniversalShaper,
  // Balinese
  batk: UniversalShaper,
  // Batak
  brah: UniversalShaper,
  // Brahmi
  bugi: UniversalShaper,
  // Buginese
  buhd: UniversalShaper,
  // Buhid
  cakm: UniversalShaper,
  // Chakma
  cham: UniversalShaper,
  // Cham
  dupl: UniversalShaper,
  // Duployan
  egyp: UniversalShaper,
  // Egyptian Hieroglyphs
  gran: UniversalShaper,
  // Grantha
  hano: UniversalShaper,
  // Hanunoo
  java: UniversalShaper,
  // Javanese
  kthi: UniversalShaper,
  // Kaithi
  kali: UniversalShaper,
  // Kayah Li
  khar: UniversalShaper,
  // Kharoshthi
  khoj: UniversalShaper,
  // Khojki
  sind: UniversalShaper,
  // Khudawadi
  lepc: UniversalShaper,
  // Lepcha
  limb: UniversalShaper,
  // Limbu
  mahj: UniversalShaper,
  // Mahajani
  // mand: UniversalShaper, // Mandaic
  // mani: UniversalShaper, // Manichaean
  mtei: UniversalShaper,
  // Meitei Mayek
  modi: UniversalShaper,
  // Modi
  // mong: UniversalShaper, // Mongolian
  // 'nko ': UniversalShaper, // N’Ko
  hmng: UniversalShaper,
  // Pahawh Hmong
  // phag: UniversalShaper, // Phags-pa
  // phlp: UniversalShaper, // Psalter Pahlavi
  rjng: UniversalShaper,
  // Rejang
  saur: UniversalShaper,
  // Saurashtra
  shrd: UniversalShaper,
  // Sharada
  sidd: UniversalShaper,
  // Siddham
  sinh: UniversalShaper,
  // Sinhala
  sund: UniversalShaper,
  // Sundanese
  sylo: UniversalShaper,
  // Syloti Nagri
  tglg: UniversalShaper,
  // Tagalog
  tagb: UniversalShaper,
  // Tagbanwa
  tale: UniversalShaper,
  // Tai Le
  lana: UniversalShaper,
  // Tai Tham
  tavt: UniversalShaper,
  // Tai Viet
  takr: UniversalShaper,
  // Takri
  tibt: UniversalShaper,
  // Tibetan
  tfng: UniversalShaper,
  // Tifinagh
  tirh: UniversalShaper,
  // Tirhuta
  latn: DefaultShaper,
  // Latin
  DFLT: DefaultShaper // Default
};
function choose(script) {
  if (!Array.isArray(script)) {
    script = [script];
  }
  for (var _iterator59 = _createForOfIteratorHelperLoose(script), _step59; !(_step59 = _iterator59()).done;) {
    var s = _step59.value;
    var shaper = SHAPERS[s];
    if (shaper) {
      return shaper;
    }
  }
  return DefaultShaper;
}
var GSUBProcessor = /*#__PURE__*/function (_OTProcessor) {
  function GSUBProcessor() {
    return _OTProcessor.apply(this, arguments) || this;
  }
  _inheritsLoose(GSUBProcessor, _OTProcessor);
  var _proto27 = GSUBProcessor.prototype;
  _proto27.applyLookup = function applyLookup(lookupType, table) {
    var _this7 = this;
    switch (lookupType) {
      case 1:
        {
          // Single Substitution
          var index = this.coverageIndex(table.coverage);
          if (index === -1) {
            return false;
          }
          var glyph = this.glyphIterator.cur;
          switch (table.version) {
            case 1:
              glyph.id = glyph.id + table.deltaGlyphID & 0xffff;
              break;
            case 2:
              glyph.id = table.substitute.get(index);
              break;
          }
          return true;
        }
      case 2:
        {
          // Multiple Substitution
          var _index = this.coverageIndex(table.coverage);
          if (_index !== -1) {
            var _this$glyphs2;
            var sequence = table.sequences.get(_index);
            if (sequence.length === 0) {
              // If the sequence length is zero, delete the glyph.
              // The OpenType spec disallows this, but seems like Harfbuzz and Uniscribe allow it.
              this.glyphs.splice(this.glyphIterator.index, 1);
              return true;
            }
            this.glyphIterator.cur.id = sequence[0];
            this.glyphIterator.cur.ligatureComponent = 0;
            var features = this.glyphIterator.cur.features;
            var curGlyph = this.glyphIterator.cur;
            var replacement = sequence.slice(1).map(function (gid, i) {
              var glyph = new GlyphInfo(_this7.font, gid, undefined, features);
              glyph.shaperInfo = curGlyph.shaperInfo;
              glyph.isLigated = curGlyph.isLigated;
              glyph.ligatureComponent = i + 1;
              glyph.substituted = true;
              glyph.isMultiplied = true;
              return glyph;
            });
            (_this$glyphs2 = this.glyphs).splice.apply(_this$glyphs2, [this.glyphIterator.index + 1, 0].concat(replacement));
            return true;
          }
          return false;
        }
      case 3:
        {
          // Alternate Substitution
          var _index2 = this.coverageIndex(table.coverage);
          if (_index2 !== -1) {
            var USER_INDEX = 0; // TODO

            this.glyphIterator.cur.id = table.alternateSet.get(_index2)[USER_INDEX];
            return true;
          }
          return false;
        }
      case 4:
        {
          // Ligature Substitution
          var _index3 = this.coverageIndex(table.coverage);
          if (_index3 === -1) {
            return false;
          }
          for (var _iterator60 = _createForOfIteratorHelperLoose(table.ligatureSets.get(_index3)), _step60; !(_step60 = _iterator60()).done;) {
            var ligature = _step60.value;
            var matched = this.sequenceMatchIndices(1, ligature.components);
            if (!matched) {
              continue;
            }
            var _curGlyph = this.glyphIterator.cur; // Concatenate all of the characters the new ligature will represent

            var characters = _curGlyph.codePoints.slice();
            for (var _iterator61 = _createForOfIteratorHelperLoose(matched), _step61; !(_step61 = _iterator61()).done;) {
              var _characters;
              var _index4 = _step61.value;
              (_characters = characters).push.apply(_characters, this.glyphs[_index4].codePoints);
            } // Create the replacement ligature glyph

            var ligatureGlyph = new GlyphInfo(this.font, ligature.glyph, characters, _curGlyph.features);
            ligatureGlyph.shaperInfo = _curGlyph.shaperInfo;
            ligatureGlyph.isLigated = true;
            ligatureGlyph.substituted = true; // From Harfbuzz:
            // - If it *is* a mark ligature, we don't allocate a new ligature id, and leave
            //   the ligature to keep its old ligature id.  This will allow it to attach to
            //   a base ligature in GPOS.  Eg. if the sequence is: LAM,LAM,SHADDA,FATHA,HEH,
            //   and LAM,LAM,HEH for a ligature, they will leave SHADDA and FATHA with a
            //   ligature id and component value of 2.  Then if SHADDA,FATHA form a ligature
            //   later, we don't want them to lose their ligature id/component, otherwise
            //   GPOS will fail to correctly position the mark ligature on top of the
            //   LAM,LAM,HEH ligature. See https://bugzilla.gnome.org/show_bug.cgi?id=676343
            //
            // - If a ligature is formed of components that some of which are also ligatures
            //   themselves, and those ligature components had marks attached to *their*
            //   components, we have to attach the marks to the new ligature component
            //   positions!  Now *that*'s tricky!  And these marks may be following the
            //   last component of the whole sequence, so we should loop forward looking
            //   for them and update them.
            //
            //   Eg. the sequence is LAM,LAM,SHADDA,FATHA,HEH, and the font first forms a
            //   'calt' ligature of LAM,HEH, leaving the SHADDA and FATHA with a ligature
            //   id and component == 1.  Now, during 'liga', the LAM and the LAM-HEH ligature
            //   form a LAM-LAM-HEH ligature.  We need to reassign the SHADDA and FATHA to
            //   the new ligature with a component value of 2.
            //
            //   This in fact happened to a font...  See https://bugzilla.gnome.org/show_bug.cgi?id=437633

            var isMarkLigature = _curGlyph.isMark;
            for (var i = 0; i < matched.length && isMarkLigature; i++) {
              isMarkLigature = this.glyphs[matched[i]].isMark;
            }
            ligatureGlyph.ligatureID = isMarkLigature ? null : this.ligatureID++;
            var lastLigID = _curGlyph.ligatureID;
            var lastNumComps = _curGlyph.codePoints.length;
            var curComps = lastNumComps;
            var idx = this.glyphIterator.index + 1; // Set ligatureID and ligatureComponent on glyphs that were skipped in the matched sequence.
            // This allows GPOS to attach marks to the correct ligature components.

            for (var _iterator62 = _createForOfIteratorHelperLoose(matched), _step62; !(_step62 = _iterator62()).done;) {
              var matchIndex = _step62.value;
              // Don't assign new ligature components for mark ligatures (see above)
              if (isMarkLigature) {
                idx = matchIndex;
              } else {
                while (idx < matchIndex) {
                  var ligatureComponent = curComps - lastNumComps + Math.min(this.glyphs[idx].ligatureComponent || 1, lastNumComps);
                  this.glyphs[idx].ligatureID = ligatureGlyph.ligatureID;
                  this.glyphs[idx].ligatureComponent = ligatureComponent;
                  idx++;
                }
              }
              lastLigID = this.glyphs[idx].ligatureID;
              lastNumComps = this.glyphs[idx].codePoints.length;
              curComps += lastNumComps;
              idx++; // skip base glyph
            } // Adjust ligature components for any marks following

            if (lastLigID && !isMarkLigature) {
              for (var _i = idx; _i < this.glyphs.length; _i++) {
                if (this.glyphs[_i].ligatureID === lastLigID) {
                  var ligatureComponent = curComps - lastNumComps + Math.min(this.glyphs[_i].ligatureComponent || 1, lastNumComps);
                  this.glyphs[_i].ligatureComponent = ligatureComponent;
                } else {
                  break;
                }
              }
            } // Delete the matched glyphs, and replace the current glyph with the ligature glyph

            for (var _i2 = matched.length - 1; _i2 >= 0; _i2--) {
              this.glyphs.splice(matched[_i2], 1);
            }
            this.glyphs[this.glyphIterator.index] = ligatureGlyph;
            return true;
          }
          return false;
        }
      case 5:
        // Contextual Substitution
        return this.applyContext(table);
      case 6:
        // Chaining Contextual Substitution
        return this.applyChainingContext(table);
      case 7:
        // Extension Substitution
        return this.applyLookup(table.lookupType, table.extension);
      default:
        throw new Error("GSUB lookupType ".concat(lookupType, " is not supported"));
    }
  };
  return GSUBProcessor;
}(OTProcessor);
var GPOSProcessor = /*#__PURE__*/function (_OTProcessor2) {
  function GPOSProcessor() {
    return _OTProcessor2.apply(this, arguments) || this;
  }
  _inheritsLoose(GPOSProcessor, _OTProcessor2);
  var _proto28 = GPOSProcessor.prototype;
  _proto28.applyPositionValue = function applyPositionValue(sequenceIndex, value) {
    var position = this.positions[this.glyphIterator.peekIndex(sequenceIndex)];
    if (value.xAdvance != null) {
      position.xAdvance += value.xAdvance;
    }
    if (value.yAdvance != null) {
      position.yAdvance += value.yAdvance;
    }
    if (value.xPlacement != null) {
      position.xOffset += value.xPlacement;
    }
    if (value.yPlacement != null) {
      position.yOffset += value.yPlacement;
    } // Adjustments for font variations

    var variationProcessor = this.font._variationProcessor;
    var variationStore = this.font.GDEF && this.font.GDEF.itemVariationStore;
    if (variationProcessor && variationStore) {
      if (value.xPlaDevice) {
        position.xOffset += variationProcessor.getDelta(variationStore, value.xPlaDevice.a, value.xPlaDevice.b);
      }
      if (value.yPlaDevice) {
        position.yOffset += variationProcessor.getDelta(variationStore, value.yPlaDevice.a, value.yPlaDevice.b);
      }
      if (value.xAdvDevice) {
        position.xAdvance += variationProcessor.getDelta(variationStore, value.xAdvDevice.a, value.xAdvDevice.b);
      }
      if (value.yAdvDevice) {
        position.yAdvance += variationProcessor.getDelta(variationStore, value.yAdvDevice.a, value.yAdvDevice.b);
      }
    } // TODO: device tables
  };
  _proto28.applyLookup = function applyLookup(lookupType, table) {
    switch (lookupType) {
      case 1:
        {
          // Single positioning value
          var index = this.coverageIndex(table.coverage);
          if (index === -1) {
            return false;
          }
          switch (table.version) {
            case 1:
              this.applyPositionValue(0, table.value);
              break;
            case 2:
              this.applyPositionValue(0, table.values.get(index));
              break;
          }
          return true;
        }
      case 2:
        {
          // Pair Adjustment Positioning
          var nextGlyph = this.glyphIterator.peek();
          if (!nextGlyph) {
            return false;
          }
          var _index = this.coverageIndex(table.coverage);
          if (_index === -1) {
            return false;
          }
          switch (table.version) {
            case 1:
              // Adjustments for glyph pairs
              var set = table.pairSets.get(_index);
              for (var _iterator63 = _createForOfIteratorHelperLoose(set), _step63; !(_step63 = _iterator63()).done;) {
                var _pair = _step63.value;
                if (_pair.secondGlyph === nextGlyph.id) {
                  this.applyPositionValue(0, _pair.value1);
                  this.applyPositionValue(1, _pair.value2);
                  return true;
                }
              }
              return false;
            case 2:
              // Class pair adjustment
              var class1 = this.getClassID(this.glyphIterator.cur.id, table.classDef1);
              var class2 = this.getClassID(nextGlyph.id, table.classDef2);
              if (class1 === -1 || class2 === -1) {
                return false;
              }
              var pair = table.classRecords.get(class1).get(class2);
              this.applyPositionValue(0, pair.value1);
              this.applyPositionValue(1, pair.value2);
              return true;
          }
        }
      case 3:
        {
          // Cursive Attachment Positioning
          var nextIndex = this.glyphIterator.peekIndex();
          var _nextGlyph = this.glyphs[nextIndex];
          if (!_nextGlyph) {
            return false;
          }
          var curRecord = table.entryExitRecords[this.coverageIndex(table.coverage)];
          if (!curRecord || !curRecord.exitAnchor) {
            return false;
          }
          var nextRecord = table.entryExitRecords[this.coverageIndex(table.coverage, _nextGlyph.id)];
          if (!nextRecord || !nextRecord.entryAnchor) {
            return false;
          }
          var entry = this.getAnchor(nextRecord.entryAnchor);
          var exit = this.getAnchor(curRecord.exitAnchor);
          var cur = this.positions[this.glyphIterator.index];
          var next = this.positions[nextIndex];
          switch (this.direction) {
            case 'ltr':
              cur.xAdvance = exit.x + cur.xOffset;
              var d = entry.x + next.xOffset;
              next.xAdvance -= d;
              next.xOffset -= d;
              break;
            case 'rtl':
              d = exit.x + cur.xOffset;
              cur.xAdvance -= d;
              cur.xOffset -= d;
              next.xAdvance = entry.x + next.xOffset;
              break;
          }
          if (this.glyphIterator.flags.rightToLeft) {
            this.glyphIterator.cur.cursiveAttachment = nextIndex;
            cur.yOffset = entry.y - exit.y;
          } else {
            _nextGlyph.cursiveAttachment = this.glyphIterator.index;
            cur.yOffset = exit.y - entry.y;
          }
          return true;
        }
      case 4:
        {
          // Mark to base positioning
          var markIndex = this.coverageIndex(table.markCoverage);
          if (markIndex === -1) {
            return false;
          } // search backward for a base glyph

          var baseGlyphIndex = this.glyphIterator.index;
          while (--baseGlyphIndex >= 0 && (this.glyphs[baseGlyphIndex].isMark || this.glyphs[baseGlyphIndex].ligatureComponent > 0)) {}
          if (baseGlyphIndex < 0) {
            return false;
          }
          var baseIndex = this.coverageIndex(table.baseCoverage, this.glyphs[baseGlyphIndex].id);
          if (baseIndex === -1) {
            return false;
          }
          var markRecord = table.markArray[markIndex];
          var baseAnchor = table.baseArray[baseIndex][markRecord.class];
          this.applyAnchor(markRecord, baseAnchor, baseGlyphIndex);
          return true;
        }
      case 5:
        {
          // Mark to ligature positioning
          var _markIndex = this.coverageIndex(table.markCoverage);
          if (_markIndex === -1) {
            return false;
          } // search backward for a base glyph

          var _baseGlyphIndex = this.glyphIterator.index;
          while (--_baseGlyphIndex >= 0 && this.glyphs[_baseGlyphIndex].isMark) {}
          if (_baseGlyphIndex < 0) {
            return false;
          }
          var ligIndex = this.coverageIndex(table.ligatureCoverage, this.glyphs[_baseGlyphIndex].id);
          if (ligIndex === -1) {
            return false;
          }
          var ligAttach = table.ligatureArray[ligIndex];
          var markGlyph = this.glyphIterator.cur;
          var ligGlyph = this.glyphs[_baseGlyphIndex];
          var compIndex = ligGlyph.ligatureID && ligGlyph.ligatureID === markGlyph.ligatureID && markGlyph.ligatureComponent > 0 ? Math.min(markGlyph.ligatureComponent, ligGlyph.codePoints.length) - 1 : ligGlyph.codePoints.length - 1;
          var _markRecord = table.markArray[_markIndex];
          var _baseAnchor = ligAttach[compIndex][_markRecord.class];
          this.applyAnchor(_markRecord, _baseAnchor, _baseGlyphIndex);
          return true;
        }
      case 6:
        {
          // Mark to mark positioning
          var mark1Index = this.coverageIndex(table.mark1Coverage);
          if (mark1Index === -1) {
            return false;
          } // get the previous mark to attach to

          var prevIndex = this.glyphIterator.peekIndex(-1);
          var prev = this.glyphs[prevIndex];
          if (!prev || !prev.isMark) {
            return false;
          }
          var _cur = this.glyphIterator.cur; // The following logic was borrowed from Harfbuzz

          var good = false;
          if (_cur.ligatureID === prev.ligatureID) {
            if (!_cur.ligatureID) {
              // Marks belonging to the same base
              good = true;
            } else if (_cur.ligatureComponent === prev.ligatureComponent) {
              // Marks belonging to the same ligature component
              good = true;
            }
          } else {
            // If ligature ids don't match, it may be the case that one of the marks
            // itself is a ligature, in which case match.
            if (_cur.ligatureID && !_cur.ligatureComponent || prev.ligatureID && !prev.ligatureComponent) {
              good = true;
            }
          }
          if (!good) {
            return false;
          }
          var mark2Index = this.coverageIndex(table.mark2Coverage, prev.id);
          if (mark2Index === -1) {
            return false;
          }
          var _markRecord2 = table.mark1Array[mark1Index];
          var _baseAnchor2 = table.mark2Array[mark2Index][_markRecord2.class];
          this.applyAnchor(_markRecord2, _baseAnchor2, prevIndex);
          return true;
        }
      case 7:
        // Contextual positioning
        return this.applyContext(table);
      case 8:
        // Chaining contextual positioning
        return this.applyChainingContext(table);
      case 9:
        // Extension positioning
        return this.applyLookup(table.lookupType, table.extension);
      default:
        throw new Error("Unsupported GPOS table: ".concat(lookupType));
    }
  };
  _proto28.applyAnchor = function applyAnchor(markRecord, baseAnchor, baseGlyphIndex) {
    var baseCoords = this.getAnchor(baseAnchor);
    var markCoords = this.getAnchor(markRecord.markAnchor);
    var basePos = this.positions[baseGlyphIndex];
    var markPos = this.positions[this.glyphIterator.index];
    markPos.xOffset = baseCoords.x - markCoords.x;
    markPos.yOffset = baseCoords.y - markCoords.y;
    this.glyphIterator.cur.markAttachment = baseGlyphIndex;
  };
  _proto28.getAnchor = function getAnchor(anchor) {
    // TODO: contour point, device tables
    var x = anchor.xCoordinate;
    var y = anchor.yCoordinate; // Adjustments for font variations

    var variationProcessor = this.font._variationProcessor;
    var variationStore = this.font.GDEF && this.font.GDEF.itemVariationStore;
    if (variationProcessor && variationStore) {
      if (anchor.xDeviceTable) {
        x += variationProcessor.getDelta(variationStore, anchor.xDeviceTable.a, anchor.xDeviceTable.b);
      }
      if (anchor.yDeviceTable) {
        y += variationProcessor.getDelta(variationStore, anchor.yDeviceTable.a, anchor.yDeviceTable.b);
      }
    }
    return {
      x: x,
      y: y
    };
  };
  _proto28.applyFeatures = function applyFeatures(userFeatures, glyphs, advances) {
    _OTProcessor2.prototype.applyFeatures.call(this, userFeatures, glyphs, advances);
    for (var i = 0; i < this.glyphs.length; i++) {
      this.fixCursiveAttachment(i);
    }
    this.fixMarkAttachment();
  };
  _proto28.fixCursiveAttachment = function fixCursiveAttachment(i) {
    var glyph = this.glyphs[i];
    if (glyph.cursiveAttachment != null) {
      var j = glyph.cursiveAttachment;
      glyph.cursiveAttachment = null;
      this.fixCursiveAttachment(j);
      this.positions[i].yOffset += this.positions[j].yOffset;
    }
  };
  _proto28.fixMarkAttachment = function fixMarkAttachment() {
    for (var i = 0; i < this.glyphs.length; i++) {
      var glyph = this.glyphs[i];
      if (glyph.markAttachment != null) {
        var j = glyph.markAttachment;
        this.positions[i].xOffset += this.positions[j].xOffset;
        this.positions[i].yOffset += this.positions[j].yOffset;
        if (this.direction === 'ltr') {
          for (var k = j; k < i; k++) {
            this.positions[i].xOffset -= this.positions[k].xAdvance;
            this.positions[i].yOffset -= this.positions[k].yAdvance;
          }
        } else {
          for (var _k = j + 1; _k < i + 1; _k++) {
            this.positions[i].xOffset += this.positions[_k].xAdvance;
            this.positions[i].yOffset += this.positions[_k].yAdvance;
          }
        }
      }
    }
  };
  return GPOSProcessor;
}(OTProcessor);
var OTLayoutEngine = /*#__PURE__*/function () {
  function OTLayoutEngine(font) {
    this.font = font;
    this.glyphInfos = null;
    this.plan = null;
    this.GSUBProcessor = null;
    this.GPOSProcessor = null;
    this.fallbackPosition = true;
    if (font.GSUB) {
      this.GSUBProcessor = new GSUBProcessor(font, font.GSUB);
    }
    if (font.GPOS) {
      this.GPOSProcessor = new GPOSProcessor(font, font.GPOS);
    }
  }
  var _proto29 = OTLayoutEngine.prototype;
  _proto29.setup = function setup(glyphRun) {
    var _this8 = this;
    // Map glyphs to GlyphInfo objects so data can be passed between
    // GSUB and GPOS without mutating the real (shared) Glyph objects.
    this.glyphInfos = glyphRun.glyphs.map(function (glyph) {
      return new GlyphInfo(_this8.font, glyph.id, [].concat(glyph.codePoints));
    }); // Select a script based on what is available in GSUB/GPOS.

    var script = null;
    if (this.GPOSProcessor) {
      script = this.GPOSProcessor.selectScript(glyphRun.script, glyphRun.language, glyphRun.direction);
    }
    if (this.GSUBProcessor) {
      script = this.GSUBProcessor.selectScript(glyphRun.script, glyphRun.language, glyphRun.direction);
    } // Choose a shaper based on the script, and setup a shaping plan.
    // This determines which features to apply to which glyphs.

    this.shaper = choose(script);
    this.plan = new ShapingPlan(this.font, script, glyphRun.direction);
    this.shaper.plan(this.plan, this.glyphInfos, glyphRun.features); // Assign chosen features to output glyph run

    for (var key in this.plan.allFeatures) {
      glyphRun.features[key] = true;
    }
  };
  _proto29.substitute = function substitute(glyphRun) {
    var _this9 = this;
    if (this.GSUBProcessor) {
      this.plan.process(this.GSUBProcessor, this.glyphInfos); // Map glyph infos back to normal Glyph objects

      glyphRun.glyphs = this.glyphInfos.map(function (glyphInfo) {
        return _this9.font.getGlyph(glyphInfo.id, glyphInfo.codePoints);
      });
    }
  };
  _proto29.position = function position(glyphRun) {
    if (this.shaper.zeroMarkWidths === 'BEFORE_GPOS') {
      this.zeroMarkAdvances(glyphRun.positions);
    }
    if (this.GPOSProcessor) {
      this.plan.process(this.GPOSProcessor, this.glyphInfos, glyphRun.positions);
    }
    if (this.shaper.zeroMarkWidths === 'AFTER_GPOS') {
      this.zeroMarkAdvances(glyphRun.positions);
    } // Reverse the glyphs and positions if the script is right-to-left

    if (glyphRun.direction === 'rtl') {
      glyphRun.glyphs.reverse();
      glyphRun.positions.reverse();
    }
    return this.GPOSProcessor && this.GPOSProcessor.features;
  };
  _proto29.zeroMarkAdvances = function zeroMarkAdvances(positions) {
    for (var i = 0; i < this.glyphInfos.length; i++) {
      if (this.glyphInfos[i].isMark) {
        positions[i].xAdvance = 0;
        positions[i].yAdvance = 0;
      }
    }
  };
  _proto29.cleanup = function cleanup() {
    this.glyphInfos = null;
    this.plan = null;
    this.shaper = null;
  };
  _proto29.getAvailableFeatures = function getAvailableFeatures(script, language) {
    var features = [];
    if (this.GSUBProcessor) {
      this.GSUBProcessor.selectScript(script, language);
      features.push.apply(features, Object.keys(this.GSUBProcessor.features));
    }
    if (this.GPOSProcessor) {
      this.GPOSProcessor.selectScript(script, language);
      features.push.apply(features, Object.keys(this.GPOSProcessor.features));
    }
    return features;
  };
  return OTLayoutEngine;
}();
var LayoutEngine = /*#__PURE__*/function () {
  function LayoutEngine(font) {
    this.font = font;
    this.unicodeLayoutEngine = null;
    this.kernProcessor = null; // Choose an advanced layout engine. We try the AAT morx table first since more
    // scripts are currently supported because the shaping logic is built into the font.

    if (this.font.morx) {
      this.engine = new AATLayoutEngine(this.font);
    } else if (this.font.GSUB || this.font.GPOS) {
      this.engine = new OTLayoutEngine(this.font);
    }
  }
  var _proto30 = LayoutEngine.prototype;
  _proto30.layout = function layout(string, features, script, language, direction) {
    // Make the features parameter optional
    if (typeof features === 'string') {
      direction = language;
      language = script;
      script = features;
      features = [];
    } // Map string to glyphs if needed

    if (typeof string === 'string') {
      // Attempt to detect the script from the string if not provided.
      if (script == null) {
        script = forString(string);
      }
      var glyphs = this.font.glyphsForString(string);
    } else {
      // Attempt to detect the script from the glyph code points if not provided.
      if (script == null) {
        var codePoints = [];
        for (var _iterator64 = _createForOfIteratorHelperLoose(string), _step64; !(_step64 = _iterator64()).done;) {
          var glyph = _step64.value;
          codePoints.push.apply(codePoints, glyph.codePoints);
        }
        script = forCodePoints(codePoints);
      }
      var glyphs = string;
    }
    var glyphRun = new GlyphRun(glyphs, features, script, language, direction); // Return early if there are no glyphs

    if (glyphs.length === 0) {
      glyphRun.positions = [];
      return glyphRun;
    } // Setup the advanced layout engine

    if (this.engine && this.engine.setup) {
      this.engine.setup(glyphRun);
    } // Substitute and position the glyphs

    this.substitute(glyphRun);
    this.position(glyphRun);
    this.hideDefaultIgnorables(glyphRun.glyphs, glyphRun.positions); // Let the layout engine clean up any state it might have

    if (this.engine && this.engine.cleanup) {
      this.engine.cleanup();
    }
    return glyphRun;
  };
  _proto30.substitute = function substitute(glyphRun) {
    // Call the advanced layout engine to make substitutions
    if (this.engine && this.engine.substitute) {
      this.engine.substitute(glyphRun);
    }
  };
  _proto30.position = function position(glyphRun) {
    // Get initial glyph positions
    glyphRun.positions = glyphRun.glyphs.map(function (glyph) {
      return new GlyphPosition(glyph.advanceWidth);
    });
    var positioned = null; // Call the advanced layout engine. Returns the features applied.

    if (this.engine && this.engine.position) {
      positioned = this.engine.position(glyphRun);
    } // if there is no GPOS table, use unicode properties to position marks.

    if (!positioned && (!this.engine || this.engine.fallbackPosition)) {
      if (!this.unicodeLayoutEngine) {
        this.unicodeLayoutEngine = new UnicodeLayoutEngine(this.font);
      }
      this.unicodeLayoutEngine.positionGlyphs(glyphRun.glyphs, glyphRun.positions);
    } // if kerning is not supported by GPOS, do kerning with the TrueType/AAT kern table

    if ((!positioned || !positioned.kern) && glyphRun.features.kern !== false && this.font.kern) {
      if (!this.kernProcessor) {
        this.kernProcessor = new KernProcessor(this.font);
      }
      this.kernProcessor.process(glyphRun.glyphs, glyphRun.positions);
      glyphRun.features.kern = true;
    }
  };
  _proto30.hideDefaultIgnorables = function hideDefaultIgnorables(glyphs, positions) {
    var space = this.font.glyphForCodePoint(0x20);
    for (var i = 0; i < glyphs.length; i++) {
      if (this.isDefaultIgnorable(glyphs[i].codePoints[0])) {
        glyphs[i] = space;
        positions[i].xAdvance = 0;
        positions[i].yAdvance = 0;
      }
    }
  };
  _proto30.isDefaultIgnorable = function isDefaultIgnorable(ch) {
    // From DerivedCoreProperties.txt in the Unicode database,
    // minus U+115F, U+1160, U+3164 and U+FFA0, which is what
    // Harfbuzz and Uniscribe do.
    var plane = ch >> 16;
    if (plane === 0) {
      // BMP
      switch (ch >> 8) {
        case 0x00:
          return ch === 0x00AD;
        case 0x03:
          return ch === 0x034F;
        case 0x06:
          return ch === 0x061C;
        case 0x17:
          return 0x17B4 <= ch && ch <= 0x17B5;
        case 0x18:
          return 0x180B <= ch && ch <= 0x180E;
        case 0x20:
          return 0x200B <= ch && ch <= 0x200F || 0x202A <= ch && ch <= 0x202E || 0x2060 <= ch && ch <= 0x206F;
        case 0xFE:
          return 0xFE00 <= ch && ch <= 0xFE0F || ch === 0xFEFF;
        case 0xFF:
          return 0xFFF0 <= ch && ch <= 0xFFF8;
        default:
          return false;
      }
    } else {
      // Other planes
      switch (plane) {
        case 0x01:
          return 0x1BCA0 <= ch && ch <= 0x1BCA3 || 0x1D173 <= ch && ch <= 0x1D17A;
        case 0x0E:
          return 0xE0000 <= ch && ch <= 0xE0FFF;
        default:
          return false;
      }
    }
  };
  _proto30.getAvailableFeatures = function getAvailableFeatures(script, language) {
    var features = [];
    if (this.engine) {
      features.push.apply(features, this.engine.getAvailableFeatures(script, language));
    }
    if (this.font.kern && features.indexOf('kern') === -1) {
      features.push('kern');
    }
    return features;
  };
  _proto30.stringsForGlyph = function stringsForGlyph(gid) {
    var result = new Set();
    var codePoints = this.font._cmapProcessor.codePointsForGlyph(gid);
    for (var _iterator65 = _createForOfIteratorHelperLoose(codePoints), _step65; !(_step65 = _iterator65()).done;) {
      var codePoint = _step65.value;
      result.add(String.fromCodePoint(codePoint));
    }
    if (this.engine && this.engine.stringsForGlyph) {
      for (var _iterator66 = _createForOfIteratorHelperLoose(this.engine.stringsForGlyph(gid)), _step66; !(_step66 = _iterator66()).done;) {
        var string = _step66.value;
        result.add(string);
      }
    }
    return Array.from(result);
  };
  return LayoutEngine;
}();
var SVG_COMMANDS = {
  moveTo: 'M',
  lineTo: 'L',
  quadraticCurveTo: 'Q',
  bezierCurveTo: 'C',
  closePath: 'Z'
};
/**
 * Path objects are returned by glyphs and represent the actual
 * vector outlines for each glyph in the font. Paths can be converted
 * to SVG path data strings, or to functions that can be applied to
 * render the path to a graphics context.
 */
var Path = /*#__PURE__*/function () {
  function Path() {
    this.commands = [];
    this._bbox = null;
    this._cbox = null;
  }
  /**
   * Compiles the path to a JavaScript function that can be applied with
   * a graphics context in order to render the path.
   * @return {string}
   */
  var _proto31 = Path.prototype;
  _proto31.toFunction = function toFunction() {
    var _this10 = this;
    return function (ctx) {
      _this10.commands.forEach(function (c) {
        return ctx[c.command].apply(ctx, c.args);
      });
    };
  }
  /**
   * Converts the path to an SVG path data string
   * @return {string}
   */;
  _proto31.toSVG = function toSVG() {
    var cmds = this.commands.map(function (c) {
      var args = c.args.map(function (arg) {
        return Math.round(arg * 100) / 100;
      });
      return "".concat(SVG_COMMANDS[c.command]).concat(args.join(' '));
    });
    return cmds.join('');
  }
  /**
   * Gets the "control box" of a path.
   * This is like the bounding box, but it includes all points including
   * control points of bezier segments and is much faster to compute than
   * the real bounding box.
   * @type {BBox}
   */;
  /**
   * Applies a mapping function to each point in the path.
   * @param {function} fn
   * @return {Path}
   */
  _proto31.mapPoints = function mapPoints(fn) {
    var path = new Path();
    for (var _iterator67 = _createForOfIteratorHelperLoose(this.commands), _step67; !(_step67 = _iterator67()).done;) {
      var c = _step67.value;
      var args = [];
      for (var i = 0; i < c.args.length; i += 2) {
        var _fn = fn(c.args[i], c.args[i + 1]),
          x = _fn[0],
          y = _fn[1];
        args.push(x, y);
      }
      path[c.command].apply(path, args);
    }
    return path;
  }
  /**
   * Transforms the path by the given matrix.
   */;
  _proto31.transform = function transform(m0, m1, m2, m3, m4, m5) {
    return this.mapPoints(function (x, y) {
      x = m0 * x + m2 * y + m4;
      y = m1 * x + m3 * y + m5;
      return [x, y];
    });
  }
  /**
   * Translates the path by the given offset.
   */;
  _proto31.translate = function translate(x, y) {
    return this.transform(1, 0, 0, 1, x, y);
  }
  /**
   * Rotates the path by the given angle (in radians).
   */;
  _proto31.rotate = function rotate(angle) {
    var cos = Math.cos(angle);
    var sin = Math.sin(angle);
    return this.transform(cos, sin, -sin, cos, 0, 0);
  }
  /**
   * Scales the path.
   */;
  _proto31.scale = function scale(scaleX, scaleY) {
    if (scaleY === void 0) {
      scaleY = scaleX;
    }
    return this.transform(scaleX, 0, 0, scaleY, 0, 0);
  };
  return _createClass(Path, [{
    key: "cbox",
    get: function get() {
      if (!this._cbox) {
        var cbox = new BBox();
        for (var _iterator68 = _createForOfIteratorHelperLoose(this.commands), _step68; !(_step68 = _iterator68()).done;) {
          var command = _step68.value;
          for (var i = 0; i < command.args.length; i += 2) {
            cbox.addPoint(command.args[i], command.args[i + 1]);
          }
        }
        this._cbox = Object.freeze(cbox);
      }
      return this._cbox;
    }
    /**
     * Gets the exact bounding box of the path by evaluating curve segments.
     * Slower to compute than the control box, but more accurate.
     * @type {BBox}
     */
  }, {
    key: "bbox",
    get: function get() {
      if (this._bbox) {
        return this._bbox;
      }
      var bbox = new BBox();
      var cx = 0,
        cy = 0;
      var f = function f(t) {
        return Math.pow(1 - t, 3) * p0[i] + 3 * Math.pow(1 - t, 2) * t * p1[i] + 3 * (1 - t) * Math.pow(t, 2) * p2[i] + Math.pow(t, 3) * p3[i];
      };
      for (var _iterator69 = _createForOfIteratorHelperLoose(this.commands), _step69; !(_step69 = _iterator69()).done;) {
        var c = _step69.value;
        switch (c.command) {
          case 'moveTo':
          case 'lineTo':
            var _c$args = c.args,
              x = _c$args[0],
              y = _c$args[1];
            bbox.addPoint(x, y);
            cx = x;
            cy = y;
            break;
          case 'quadraticCurveTo':
          case 'bezierCurveTo':
            if (c.command === 'quadraticCurveTo') {
              // http://fontforge.org/bezier.html
              var _c$args2 = c.args,
                qp1x = _c$args2[0],
                qp1y = _c$args2[1],
                p3x = _c$args2[2],
                p3y = _c$args2[3];
              var cp1x = cx + 2 / 3 * (qp1x - cx); // CP1 = QP0 + 2/3 * (QP1-QP0)

              var cp1y = cy + 2 / 3 * (qp1y - cy);
              var cp2x = p3x + 2 / 3 * (qp1x - p3x); // CP2 = QP2 + 2/3 * (QP1-QP2)

              var cp2y = p3y + 2 / 3 * (qp1y - p3y);
            } else {
              var _c$args3 = c.args,
                cp1x = _c$args3[0],
                cp1y = _c$args3[1],
                cp2x = _c$args3[2],
                cp2y = _c$args3[3],
                p3x = _c$args3[4],
                p3y = _c$args3[5];
            } // http://blog.hackers-cafe.net/2009/06/how-to-calculate-bezier-curves-bounding.html

            bbox.addPoint(p3x, p3y);
            var p0 = [cx, cy];
            var p1 = [cp1x, cp1y];
            var p2 = [cp2x, cp2y];
            var p3 = [p3x, p3y];
            for (var i = 0; i <= 1; i++) {
              var b = 6 * p0[i] - 12 * p1[i] + 6 * p2[i];
              var a = -3 * p0[i] + 9 * p1[i] - 9 * p2[i] + 3 * p3[i];
              c = 3 * p1[i] - 3 * p0[i];
              if (a === 0) {
                if (b === 0) {
                  continue;
                }
                var t = -c / b;
                if (0 < t && t < 1) {
                  if (i === 0) {
                    bbox.addPoint(f(t), bbox.maxY);
                  } else if (i === 1) {
                    bbox.addPoint(bbox.maxX, f(t));
                  }
                }
                continue;
              }
              var b2ac = Math.pow(b, 2) - 4 * c * a;
              if (b2ac < 0) {
                continue;
              }
              var t1 = (-b + Math.sqrt(b2ac)) / (2 * a);
              if (0 < t1 && t1 < 1) {
                if (i === 0) {
                  bbox.addPoint(f(t1), bbox.maxY);
                } else if (i === 1) {
                  bbox.addPoint(bbox.maxX, f(t1));
                }
              }
              var t2 = (-b - Math.sqrt(b2ac)) / (2 * a);
              if (0 < t2 && t2 < 1) {
                if (i === 0) {
                  bbox.addPoint(f(t2), bbox.maxY);
                } else if (i === 1) {
                  bbox.addPoint(bbox.maxX, f(t2));
                }
              }
            }
            cx = p3x;
            cy = p3y;
            break;
        }
      }
      return this._bbox = Object.freeze(bbox);
    }
  }]);
}();
var _loop = function _loop(command) {
  Path.prototype[command] = function () {
    this._bbox = this._cbox = null;
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    this.commands.push({
      command: command,
      args: args
    });
    return this;
  };
};
for (var _i23 = 0, _arr = ['moveTo', 'lineTo', 'quadraticCurveTo', 'bezierCurveTo', 'closePath']; _i23 < _arr.length; _i23++) {
  var command = _arr[_i23];
  _loop(command);
}
var StandardNames = ['.notdef', '.null', 'nonmarkingreturn', 'space', 'exclam', 'quotedbl', 'numbersign', 'dollar', 'percent', 'ampersand', 'quotesingle', 'parenleft', 'parenright', 'asterisk', 'plus', 'comma', 'hyphen', 'period', 'slash', 'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'colon', 'semicolon', 'less', 'equal', 'greater', 'question', 'at', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'bracketleft', 'backslash', 'bracketright', 'asciicircum', 'underscore', 'grave', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'braceleft', 'bar', 'braceright', 'asciitilde', 'Adieresis', 'Aring', 'Ccedilla', 'Eacute', 'Ntilde', 'Odieresis', 'Udieresis', 'aacute', 'agrave', 'acircumflex', 'adieresis', 'atilde', 'aring', 'ccedilla', 'eacute', 'egrave', 'ecircumflex', 'edieresis', 'iacute', 'igrave', 'icircumflex', 'idieresis', 'ntilde', 'oacute', 'ograve', 'ocircumflex', 'odieresis', 'otilde', 'uacute', 'ugrave', 'ucircumflex', 'udieresis', 'dagger', 'degree', 'cent', 'sterling', 'section', 'bullet', 'paragraph', 'germandbls', 'registered', 'copyright', 'trademark', 'acute', 'dieresis', 'notequal', 'AE', 'Oslash', 'infinity', 'plusminus', 'lessequal', 'greaterequal', 'yen', 'mu', 'partialdiff', 'summation', 'product', 'pi', 'integral', 'ordfeminine', 'ordmasculine', 'Omega', 'ae', 'oslash', 'questiondown', 'exclamdown', 'logicalnot', 'radical', 'florin', 'approxequal', 'Delta', 'guillemotleft', 'guillemotright', 'ellipsis', 'nonbreakingspace', 'Agrave', 'Atilde', 'Otilde', 'OE', 'oe', 'endash', 'emdash', 'quotedblleft', 'quotedblright', 'quoteleft', 'quoteright', 'divide', 'lozenge', 'ydieresis', 'Ydieresis', 'fraction', 'currency', 'guilsinglleft', 'guilsinglright', 'fi', 'fl', 'daggerdbl', 'periodcentered', 'quotesinglbase', 'quotedblbase', 'perthousand', 'Acircumflex', 'Ecircumflex', 'Aacute', 'Edieresis', 'Egrave', 'Iacute', 'Icircumflex', 'Idieresis', 'Igrave', 'Oacute', 'Ocircumflex', 'apple', 'Ograve', 'Uacute', 'Ucircumflex', 'Ugrave', 'dotlessi', 'circumflex', 'tilde', 'macron', 'breve', 'dotaccent', 'ring', 'cedilla', 'hungarumlaut', 'ogonek', 'caron', 'Lslash', 'lslash', 'Scaron', 'scaron', 'Zcaron', 'zcaron', 'brokenbar', 'Eth', 'eth', 'Yacute', 'yacute', 'Thorn', 'thorn', 'minus', 'multiply', 'onesuperior', 'twosuperior', 'threesuperior', 'onehalf', 'onequarter', 'threequarters', 'franc', 'Gbreve', 'gbreve', 'Idotaccent', 'Scedilla', 'scedilla', 'Cacute', 'cacute', 'Ccaron', 'ccaron', 'dcroat'];
var _class$3;
/**
 * Glyph objects represent a glyph in the font. They have various properties for accessing metrics and
 * the actual vector path the glyph represents, and methods for rendering the glyph to a graphics context.
 *
 * You do not create glyph objects directly. They are created by various methods on the font object.
 * There are several subclasses of the base Glyph class internally that may be returned depending
 * on the font format, but they all inherit from this class.
 */

var Glyph = (_class$3 = /*#__PURE__*/function () {
  function Glyph(id, codePoints, font) {
    /**
     * The glyph id in the font
     * @type {number}
     */
    this.id = id;
    /**
     * An array of unicode code points that are represented by this glyph.
     * There can be multiple code points in the case of ligatures and other glyphs
     * that represent multiple visual characters.
     * @type {number[]}
     */

    this.codePoints = codePoints;
    this._font = font; // TODO: get this info from GDEF if available

    this.isMark = this.codePoints.length > 0 && this.codePoints.every(unicode.isMark);
    this.isLigature = this.codePoints.length > 1;
  }
  var _proto32 = Glyph.prototype;
  _proto32._getPath = function _getPath() {
    return new Path();
  };
  _proto32._getCBox = function _getCBox() {
    return this.path.cbox;
  };
  _proto32._getBBox = function _getBBox() {
    return this.path.bbox;
  };
  _proto32._getTableMetrics = function _getTableMetrics(table) {
    if (this.id < table.metrics.length) {
      return table.metrics.get(this.id);
    }
    var metric = table.metrics.get(table.metrics.length - 1);
    var res = {
      advance: metric ? metric.advance : 0,
      bearing: table.bearings.get(this.id - table.metrics.length) || 0
    };
    return res;
  };
  _proto32._getMetrics = function _getMetrics(cbox) {
    if (this._metrics) {
      return this._metrics;
    }
    var _this$_getTableMetric = this._getTableMetrics(this._font.hmtx),
      advanceWidth = _this$_getTableMetric.advance,
      leftBearing = _this$_getTableMetric.bearing; // For vertical metrics, use vmtx if available, or fall back to global data from OS/2 or hhea

    if (this._font.vmtx) {
      var _this$_getTableMetric2 = this._getTableMetrics(this._font.vmtx),
        advanceHeight = _this$_getTableMetric2.advance,
        topBearing = _this$_getTableMetric2.bearing;
    } else {
      var os2;
      if (typeof cbox === 'undefined' || cbox === null) {
        cbox = this.cbox;
      }
      if ((os2 = this._font['OS/2']) && os2.version > 0) {
        var advanceHeight = Math.abs(os2.typoAscender - os2.typoDescender);
        var topBearing = os2.typoAscender - cbox.maxY;
      } else {
        var hhea = this._font.hhea;
        var advanceHeight = Math.abs(hhea.ascent - hhea.descent);
        var topBearing = hhea.ascent - cbox.maxY;
      }
    }
    if (this._font._variationProcessor && this._font.HVAR) {
      advanceWidth += this._font._variationProcessor.getAdvanceAdjustment(this.id, this._font.HVAR);
    }
    return this._metrics = {
      advanceWidth: advanceWidth,
      advanceHeight: advanceHeight,
      leftBearing: leftBearing,
      topBearing: topBearing
    };
  }
  /**
   * The glyph’s control box.
   * This is often the same as the bounding box, but is faster to compute.
   * Because of the way bezier curves are defined, some of the control points
   * can be outside of the bounding box. Where `bbox` takes this into account,
   * `cbox` does not. Thus, cbox is less accurate, but faster to compute.
   * See [here](http://www.freetype.org/freetype2/docs/glyphs/glyphs-6.html#section-2)
   * for a more detailed description.
   *
   * @type {BBox}
   */;
  /**
   * Returns a path scaled to the given font size.
   * @param {number} size
   * @return {Path}
   */
  _proto32.getScaledPath = function getScaledPath(size) {
    var scale = 1 / this._font.unitsPerEm * size;
    return this.path.scale(scale);
  }
  /**
   * The glyph's advance width.
   * @type {number}
   */;
  _proto32._getName = function _getName() {
    var post = this._font.post;
    if (!post) {
      return null;
    }
    switch (post.version) {
      case 1:
        return StandardNames[this.id];
      case 2:
        var id = post.glyphNameIndex[this.id];
        if (id < StandardNames.length) {
          return StandardNames[id];
        }
        return post.names[id - StandardNames.length];
      case 2.5:
        return StandardNames[this.id + post.offsets[this.id]];
      case 4:
        return String.fromCharCode(post.map[this.id]);
    }
  }
  /**
   * The glyph's name
   * @type {string}
   */;
  /**
   * Renders the glyph to the given graphics context, at the specified font size.
   * @param {CanvasRenderingContext2d} ctx
   * @param {number} size
   */
  _proto32.render = function render(ctx, size) {
    ctx.save();
    var scale = 1 / this._font.head.unitsPerEm * size;
    ctx.scale(scale, scale);
    var fn = this.path.toFunction();
    fn(ctx);
    ctx.fill();
    ctx.restore();
  };
  return _createClass(Glyph, [{
    key: "cbox",
    get: function get() {
      return this._getCBox();
    }
    /**
     * The glyph’s bounding box, i.e. the rectangle that encloses the
     * glyph outline as tightly as possible.
     * @type {BBox}
     */
  }, {
    key: "bbox",
    get: function get() {
      return this._getBBox();
    }
    /**
     * A vector Path object representing the glyph outline.
     * @type {Path}
     */
  }, {
    key: "path",
    get: function get() {
      // Cache the path so we only decode it once
      // Decoding is actually performed by subclasses
      return this._getPath();
    }
  }, {
    key: "advanceWidth",
    get: function get() {
      return this._getMetrics().advanceWidth;
    }
    /**
     * The glyph's advance height.
     * @type {number}
     */
  }, {
    key: "advanceHeight",
    get: function get() {
      return this._getMetrics().advanceHeight;
    }
  }, {
    key: "ligatureCaretPositions",
    get: function get() {}
  }, {
    key: "name",
    get: function get() {
      return this._getName();
    }
  }]);
}(), _applyDecoratedDescriptor(_class$3.prototype, "cbox", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "cbox"), _class$3.prototype), _applyDecoratedDescriptor(_class$3.prototype, "bbox", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "bbox"), _class$3.prototype), _applyDecoratedDescriptor(_class$3.prototype, "path", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "path"), _class$3.prototype), _applyDecoratedDescriptor(_class$3.prototype, "advanceWidth", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "advanceWidth"), _class$3.prototype), _applyDecoratedDescriptor(_class$3.prototype, "advanceHeight", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "advanceHeight"), _class$3.prototype), _applyDecoratedDescriptor(_class$3.prototype, "name", [cache], Object.getOwnPropertyDescriptor(_class$3.prototype, "name"), _class$3.prototype), _class$3);
var GlyfHeader = new r.Struct({
  numberOfContours: r.int16,
  // if negative, this is a composite glyph
  xMin: r.int16,
  yMin: r.int16,
  xMax: r.int16,
  yMax: r.int16
}); // Flags for simple glyphs

var ON_CURVE = 1 << 0;
var X_SHORT_VECTOR = 1 << 1;
var Y_SHORT_VECTOR = 1 << 2;
var REPEAT = 1 << 3;
var SAME_X = 1 << 4;
var SAME_Y = 1 << 5; // Flags for composite glyphs

var ARG_1_AND_2_ARE_WORDS = 1 << 0;
var WE_HAVE_A_SCALE = 1 << 3;
var MORE_COMPONENTS = 1 << 5;
var WE_HAVE_AN_X_AND_Y_SCALE = 1 << 6;
var WE_HAVE_A_TWO_BY_TWO = 1 << 7;
var WE_HAVE_INSTRUCTIONS = 1 << 8;
var Point = /*#__PURE__*/function () {
  function Point(onCurve, endContour, x, y) {
    if (x === void 0) {
      x = 0;
    }
    if (y === void 0) {
      y = 0;
    }
    this.onCurve = onCurve;
    this.endContour = endContour;
    this.x = x;
    this.y = y;
  }
  var _proto33 = Point.prototype;
  _proto33.copy = function copy() {
    return new Point(this.onCurve, this.endContour, this.x, this.y);
  };
  return Point;
}(); // Represents a component in a composite glyph
var Component = function Component(glyphID, dx, dy) {
  this.glyphID = glyphID;
  this.dx = dx;
  this.dy = dy;
  this.pos = 0;
  this.scaleX = this.scaleY = 1;
  this.scale01 = this.scale10 = 0;
};
/**
 * Represents a TrueType glyph.
 */
var TTFGlyph = /*#__PURE__*/function (_Glyph) {
  function TTFGlyph() {
    return _Glyph.apply(this, arguments) || this;
  }
  _inheritsLoose(TTFGlyph, _Glyph);
  var _proto34 = TTFGlyph.prototype;
  // Parses just the glyph header and returns the bounding box
  _proto34._getCBox = function _getCBox(internal) {
    // We need to decode the glyph if variation processing is requested,
    // so it's easier just to recompute the path's cbox after decoding.
    if (this._font._variationProcessor && !internal) {
      return this.path.cbox;
    }
    var stream = this._font._getTableStream('glyf');
    stream.pos += this._font.loca.offsets[this.id];
    var glyph = GlyfHeader.decode(stream);
    var cbox = new BBox(glyph.xMin, glyph.yMin, glyph.xMax, glyph.yMax);
    return Object.freeze(cbox);
  } // Parses a single glyph coordinate
  ;
  _proto34._parseGlyphCoord = function _parseGlyphCoord(stream, prev, short, same) {
    if (short) {
      var val = stream.readUInt8();
      if (!same) {
        val = -val;
      }
      val += prev;
    } else {
      if (same) {
        var val = prev;
      } else {
        var val = prev + stream.readInt16BE();
      }
    }
    return val;
  } // Decodes the glyph data into points for simple glyphs,
  // or components for composite glyphs
  ;
  _proto34._decode = function _decode() {
    var glyfPos = this._font.loca.offsets[this.id];
    var nextPos = this._font.loca.offsets[this.id + 1]; // Nothing to do if there is no data for this glyph

    if (glyfPos === nextPos) {
      return null;
    }
    var stream = this._font._getTableStream('glyf');
    stream.pos += glyfPos;
    var startPos = stream.pos;
    var glyph = GlyfHeader.decode(stream);
    if (glyph.numberOfContours > 0) {
      this._decodeSimple(glyph, stream);
    } else if (glyph.numberOfContours < 0) {
      this._decodeComposite(glyph, stream, startPos);
    }
    return glyph;
  };
  _proto34._decodeSimple = function _decodeSimple(glyph, stream) {
    // this is a simple glyph
    glyph.points = [];
    var endPtsOfContours = new r.Array(r.uint16, glyph.numberOfContours).decode(stream);
    glyph.instructions = new r.Array(r.uint8, r.uint16).decode(stream);
    var flags = [];
    var numCoords = endPtsOfContours[endPtsOfContours.length - 1] + 1;
    while (flags.length < numCoords) {
      var flag = stream.readUInt8();
      flags.push(flag); // check for repeat flag

      if (flag & REPEAT) {
        var count = stream.readUInt8();
        for (var j = 0; j < count; j++) {
          flags.push(flag);
        }
      }
    }
    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      var point = new Point(!!(flag & ON_CURVE), endPtsOfContours.indexOf(i) >= 0, 0, 0);
      glyph.points.push(point);
    }
    var px = 0;
    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      glyph.points[i].x = px = this._parseGlyphCoord(stream, px, flag & X_SHORT_VECTOR, flag & SAME_X);
    }
    var py = 0;
    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      glyph.points[i].y = py = this._parseGlyphCoord(stream, py, flag & Y_SHORT_VECTOR, flag & SAME_Y);
    }
    if (this._font._variationProcessor) {
      var points = glyph.points.slice();
      points.push.apply(points, this._getPhantomPoints(glyph));
      this._font._variationProcessor.transformPoints(this.id, points);
      glyph.phantomPoints = points.slice(-4);
    }
    return;
  };
  _proto34._decodeComposite = function _decodeComposite(glyph, stream, offset) {
    if (offset === void 0) {
      offset = 0;
    }
    // this is a composite glyph
    glyph.components = [];
    var haveInstructions = false;
    var flags = MORE_COMPONENTS;
    while (flags & MORE_COMPONENTS) {
      flags = stream.readUInt16BE();
      var gPos = stream.pos - offset;
      var glyphID = stream.readUInt16BE();
      if (!haveInstructions) {
        haveInstructions = (flags & WE_HAVE_INSTRUCTIONS) !== 0;
      }
      if (flags & ARG_1_AND_2_ARE_WORDS) {
        var dx = stream.readInt16BE();
        var dy = stream.readInt16BE();
      } else {
        var dx = stream.readInt8();
        var dy = stream.readInt8();
      }
      var component = new Component(glyphID, dx, dy);
      component.pos = gPos;
      if (flags & WE_HAVE_A_SCALE) {
        // fixed number with 14 bits of fraction
        component.scaleX = component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      } else if (flags & WE_HAVE_AN_X_AND_Y_SCALE) {
        component.scaleX = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      } else if (flags & WE_HAVE_A_TWO_BY_TWO) {
        component.scaleX = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scale01 = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scale10 = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
        component.scaleY = (stream.readUInt8() << 24 | stream.readUInt8() << 16) / 1073741824;
      }
      glyph.components.push(component);
    }
    if (this._font._variationProcessor) {
      var points = [];
      for (var j = 0; j < glyph.components.length; j++) {
        var component = glyph.components[j];
        points.push(new Point(true, true, component.dx, component.dy));
      }
      points.push.apply(points, this._getPhantomPoints(glyph));
      this._font._variationProcessor.transformPoints(this.id, points);
      glyph.phantomPoints = points.splice(-4, 4);
      for (var i = 0; i < points.length; i++) {
        var point = points[i];
        glyph.components[i].dx = point.x;
        glyph.components[i].dy = point.y;
      }
    }
    return haveInstructions;
  };
  _proto34._getPhantomPoints = function _getPhantomPoints(glyph) {
    var cbox = this._getCBox(true);
    if (this._metrics == null) {
      this._metrics = Glyph.prototype._getMetrics.call(this, cbox);
    }
    var _this$_metrics = this._metrics,
      advanceWidth = _this$_metrics.advanceWidth,
      advanceHeight = _this$_metrics.advanceHeight,
      leftBearing = _this$_metrics.leftBearing,
      topBearing = _this$_metrics.topBearing;
    return [new Point(false, true, glyph.xMin - leftBearing, 0), new Point(false, true, glyph.xMin - leftBearing + advanceWidth, 0), new Point(false, true, 0, glyph.yMax + topBearing), new Point(false, true, 0, glyph.yMax + topBearing + advanceHeight)];
  } // Decodes font data, resolves composite glyphs, and returns an array of contours
  ;
  _proto34._getContours = function _getContours() {
    var glyph = this._decode();
    if (!glyph) {
      return [];
    }
    var points = [];
    if (glyph.numberOfContours < 0) {
      // resolve composite glyphs
      for (var _iterator70 = _createForOfIteratorHelperLoose(glyph.components), _step70; !(_step70 = _iterator70()).done;) {
        var component = _step70.value;
        var _contours = this._font.getGlyph(component.glyphID)._getContours();
        for (var i = 0; i < _contours.length; i++) {
          var contour = _contours[i];
          for (var j = 0; j < contour.length; j++) {
            var _point = contour[j];
            var x = _point.x * component.scaleX + _point.y * component.scale01 + component.dx;
            var y = _point.y * component.scaleY + _point.x * component.scale10 + component.dy;
            points.push(new Point(_point.onCurve, _point.endContour, x, y));
          }
        }
      }
    } else {
      points = glyph.points || [];
    } // Recompute and cache metrics if we performed variation processing, and don't have an HVAR table

    if (glyph.phantomPoints && !this._font.directory.tables.HVAR) {
      this._metrics.advanceWidth = glyph.phantomPoints[1].x - glyph.phantomPoints[0].x;
      this._metrics.advanceHeight = glyph.phantomPoints[3].y - glyph.phantomPoints[2].y;
      this._metrics.leftBearing = glyph.xMin - glyph.phantomPoints[0].x;
      this._metrics.topBearing = glyph.phantomPoints[2].y - glyph.yMax;
    }
    var contours = [];
    var cur = [];
    for (var k = 0; k < points.length; k++) {
      var point = points[k];
      cur.push(point);
      if (point.endContour) {
        contours.push(cur);
        cur = [];
      }
    }
    return contours;
  };
  _proto34._getMetrics = function _getMetrics() {
    if (this._metrics) {
      return this._metrics;
    }
    var cbox = this._getCBox(true);
    _Glyph.prototype._getMetrics.call(this, cbox);
    if (this._font._variationProcessor && !this._font.HVAR) {
      // No HVAR table, decode the glyph. This triggers recomputation of metrics.
      this.path;
    }
    return this._metrics;
  } // Converts contours to a Path object that can be rendered
  ;
  _proto34._getPath = function _getPath() {
    var contours = this._getContours();
    var path = new Path();
    for (var i = 0; i < contours.length; i++) {
      var contour = contours[i];
      var firstPt = contour[0];
      var lastPt = contour[contour.length - 1];
      var start = 0;
      if (firstPt.onCurve) {
        // The first point will be consumed by the moveTo command, so skip in the loop
        var curvePt = null;
        start = 1;
      } else {
        if (lastPt.onCurve) {
          // Start at the last point if the first point is off curve and the last point is on curve
          firstPt = lastPt;
        } else {
          // Start at the middle if both the first and last points are off curve
          firstPt = new Point(false, false, (firstPt.x + lastPt.x) / 2, (firstPt.y + lastPt.y) / 2);
        }
        var curvePt = firstPt;
      }
      path.moveTo(firstPt.x, firstPt.y);
      for (var j = start; j < contour.length; j++) {
        var pt = contour[j];
        var prevPt = j === 0 ? firstPt : contour[j - 1];
        if (prevPt.onCurve && pt.onCurve) {
          path.lineTo(pt.x, pt.y);
        } else if (prevPt.onCurve && !pt.onCurve) {
          var curvePt = pt;
        } else if (!prevPt.onCurve && !pt.onCurve) {
          var midX = (prevPt.x + pt.x) / 2;
          var midY = (prevPt.y + pt.y) / 2;
          path.quadraticCurveTo(prevPt.x, prevPt.y, midX, midY);
          var curvePt = pt;
        } else if (!prevPt.onCurve && pt.onCurve) {
          path.quadraticCurveTo(curvePt.x, curvePt.y, pt.x, pt.y);
          var curvePt = null;
        } else {
          throw new Error("Unknown TTF path state");
        }
      } // Connect the first and last points

      if (curvePt) {
        path.quadraticCurveTo(curvePt.x, curvePt.y, firstPt.x, firstPt.y);
      }
      path.closePath();
    }
    return path;
  };
  return TTFGlyph;
}(Glyph);
/**
 * Represents an OpenType PostScript glyph, in the Compact Font Format.
 */
var CFFGlyph = /*#__PURE__*/function (_Glyph2) {
  function CFFGlyph() {
    return _Glyph2.apply(this, arguments) || this;
  }
  _inheritsLoose(CFFGlyph, _Glyph2);
  var _proto35 = CFFGlyph.prototype;
  _proto35._getName = function _getName() {
    if (this._font.CFF2) {
      return _Glyph2.prototype._getName.call(this);
    }
    return this._font['CFF '].getGlyphName(this.id);
  };
  _proto35.bias = function bias(s) {
    if (s.length < 1240) {
      return 107;
    } else if (s.length < 33900) {
      return 1131;
    } else {
      return 32768;
    }
  };
  _proto35._getPath = function _getPath() {
    var cff = this._font.CFF2 || this._font['CFF '];
    var stream = cff.stream;
    var str = cff.topDict.CharStrings[this.id];
    var end = str.offset + str.length;
    stream.pos = str.offset;
    var path = new Path();
    var stack = [];
    var trans = [];
    var width = null;
    var nStems = 0;
    var x = 0,
      y = 0;
    var usedGsubrs;
    var usedSubrs;
    var open = false;
    this._usedGsubrs = usedGsubrs = {};
    this._usedSubrs = usedSubrs = {};
    var gsubrs = cff.globalSubrIndex || [];
    var gsubrsBias = this.bias(gsubrs);
    var privateDict = cff.privateDictForGlyph(this.id) || {};
    var subrs = privateDict.Subrs || [];
    var subrsBias = this.bias(subrs);
    var vstore = cff.topDict.vstore && cff.topDict.vstore.itemVariationStore;
    var vsindex = privateDict.vsindex;
    var variationProcessor = this._font._variationProcessor;
    function checkWidth() {
      if (width == null) {
        width = stack.shift() + privateDict.nominalWidthX;
      }
    }
    function parseStems() {
      if (stack.length % 2 !== 0) {
        checkWidth();
      }
      nStems += stack.length >> 1;
      return stack.length = 0;
    }
    function moveTo(x, y) {
      if (open) {
        path.closePath();
      }
      path.moveTo(x, y);
      open = true;
    }
    var _parse = function parse() {
      while (stream.pos < end) {
        var op = stream.readUInt8();
        if (op < 32) {
          switch (op) {
            case 1: // hstem

            case 3: // vstem

            case 18: // hstemhm

            case 23:
              // vstemhm
              parseStems();
              break;
            case 4:
              // vmoveto
              if (stack.length > 1) {
                checkWidth();
              }
              y += stack.shift();
              moveTo(x, y);
              break;
            case 5:
              // rlineto
              while (stack.length >= 2) {
                x += stack.shift();
                y += stack.shift();
                path.lineTo(x, y);
              }
              break;
            case 6: // hlineto

            case 7:
              // vlineto
              var phase = op === 6;
              while (stack.length >= 1) {
                if (phase) {
                  x += stack.shift();
                } else {
                  y += stack.shift();
                }
                path.lineTo(x, y);
                phase = !phase;
              }
              break;
            case 8:
              // rrcurveto
              while (stack.length > 0) {
                var c1x = x + stack.shift();
                var c1y = y + stack.shift();
                var c2x = c1x + stack.shift();
                var c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;
            case 10:
              // callsubr
              var index = stack.pop() + subrsBias;
              var subr = subrs[index];
              if (subr) {
                usedSubrs[index] = true;
                var p = stream.pos;
                var e = end;
                stream.pos = subr.offset;
                end = subr.offset + subr.length;
                _parse();
                stream.pos = p;
                end = e;
              }
              break;
            case 11:
              // return
              if (cff.version >= 2) {
                break;
              }
              return;
            case 14:
              // endchar
              if (cff.version >= 2) {
                break;
              }
              if (stack.length > 0) {
                checkWidth();
              }
              if (open) {
                path.closePath();
                open = false;
              }
              break;
            case 15:
              {
                // vsindex
                if (cff.version < 2) {
                  throw new Error('vsindex operator not supported in CFF v1');
                }
                vsindex = stack.pop();
                break;
              }
            case 16:
              {
                // blend
                if (cff.version < 2) {
                  throw new Error('blend operator not supported in CFF v1');
                }
                if (!variationProcessor) {
                  throw new Error('blend operator in non-variation font');
                }
                var blendVector = variationProcessor.getBlendVector(vstore, vsindex);
                var numBlends = stack.pop();
                var numOperands = numBlends * blendVector.length;
                var delta = stack.length - numOperands;
                var base = delta - numBlends;
                for (var i = 0; i < numBlends; i++) {
                  var sum = stack[base + i];
                  for (var j = 0; j < blendVector.length; j++) {
                    sum += blendVector[j] * stack[delta++];
                  }
                  stack[base + i] = sum;
                }
                while (numOperands--) {
                  stack.pop();
                }
                break;
              }
            case 19: // hintmask

            case 20:
              // cntrmask
              parseStems();
              stream.pos += nStems + 7 >> 3;
              break;
            case 21:
              // rmoveto
              if (stack.length > 2) {
                checkWidth();
              }
              x += stack.shift();
              y += stack.shift();
              moveTo(x, y);
              break;
            case 22:
              // hmoveto
              if (stack.length > 1) {
                checkWidth();
              }
              x += stack.shift();
              moveTo(x, y);
              break;
            case 24:
              // rcurveline
              while (stack.length >= 8) {
                var c1x = x + stack.shift();
                var c1y = y + stack.shift();
                var c2x = c1x + stack.shift();
                var c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              x += stack.shift();
              y += stack.shift();
              path.lineTo(x, y);
              break;
            case 25:
              // rlinecurve
              while (stack.length >= 8) {
                x += stack.shift();
                y += stack.shift();
                path.lineTo(x, y);
              }
              var c1x = x + stack.shift();
              var c1y = y + stack.shift();
              var c2x = c1x + stack.shift();
              var c2y = c1y + stack.shift();
              x = c2x + stack.shift();
              y = c2y + stack.shift();
              path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              break;
            case 26:
              // vvcurveto
              if (stack.length % 2) {
                x += stack.shift();
              }
              while (stack.length >= 4) {
                c1x = x;
                c1y = y + stack.shift();
                c2x = c1x + stack.shift();
                c2y = c1y + stack.shift();
                x = c2x;
                y = c2y + stack.shift();
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;
            case 27:
              // hhcurveto
              if (stack.length % 2) {
                y += stack.shift();
              }
              while (stack.length >= 4) {
                c1x = x + stack.shift();
                c1y = y;
                c2x = c1x + stack.shift();
                c2y = c1y + stack.shift();
                x = c2x + stack.shift();
                y = c2y;
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
              }
              break;
            case 28:
              // shortint
              stack.push(stream.readInt16BE());
              break;
            case 29:
              // callgsubr
              index = stack.pop() + gsubrsBias;
              subr = gsubrs[index];
              if (subr) {
                usedGsubrs[index] = true;
                var p = stream.pos;
                var e = end;
                stream.pos = subr.offset;
                end = subr.offset + subr.length;
                _parse();
                stream.pos = p;
                end = e;
              }
              break;
            case 30: // vhcurveto

            case 31:
              // hvcurveto
              phase = op === 31;
              while (stack.length >= 4) {
                if (phase) {
                  c1x = x + stack.shift();
                  c1y = y;
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  y = c2y + stack.shift();
                  x = c2x + (stack.length === 1 ? stack.shift() : 0);
                } else {
                  c1x = x;
                  c1y = y + stack.shift();
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  x = c2x + stack.shift();
                  y = c2y + (stack.length === 1 ? stack.shift() : 0);
                }
                path.bezierCurveTo(c1x, c1y, c2x, c2y, x, y);
                phase = !phase;
              }
              break;
            case 12:
              op = stream.readUInt8();
              switch (op) {
                case 3:
                  // and
                  var a = stack.pop();
                  var b = stack.pop();
                  stack.push(a && b ? 1 : 0);
                  break;
                case 4:
                  // or
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a || b ? 1 : 0);
                  break;
                case 5:
                  // not
                  a = stack.pop();
                  stack.push(a ? 0 : 1);
                  break;
                case 9:
                  // abs
                  a = stack.pop();
                  stack.push(Math.abs(a));
                  break;
                case 10:
                  // add
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a + b);
                  break;
                case 11:
                  // sub
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a - b);
                  break;
                case 12:
                  // div
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a / b);
                  break;
                case 14:
                  // neg
                  a = stack.pop();
                  stack.push(-a);
                  break;
                case 15:
                  // eq
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a === b ? 1 : 0);
                  break;
                case 18:
                  // drop
                  stack.pop();
                  break;
                case 20:
                  // put
                  var val = stack.pop();
                  var idx = stack.pop();
                  trans[idx] = val;
                  break;
                case 21:
                  // get
                  idx = stack.pop();
                  stack.push(trans[idx] || 0);
                  break;
                case 22:
                  // ifelse
                  var s1 = stack.pop();
                  var s2 = stack.pop();
                  var v1 = stack.pop();
                  var v2 = stack.pop();
                  stack.push(v1 <= v2 ? s1 : s2);
                  break;
                case 23:
                  // random
                  stack.push(Math.random());
                  break;
                case 24:
                  // mul
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(a * b);
                  break;
                case 26:
                  // sqrt
                  a = stack.pop();
                  stack.push(Math.sqrt(a));
                  break;
                case 27:
                  // dup
                  a = stack.pop();
                  stack.push(a, a);
                  break;
                case 28:
                  // exch
                  a = stack.pop();
                  b = stack.pop();
                  stack.push(b, a);
                  break;
                case 29:
                  // index
                  idx = stack.pop();
                  if (idx < 0) {
                    idx = 0;
                  } else if (idx > stack.length - 1) {
                    idx = stack.length - 1;
                  }
                  stack.push(stack[idx]);
                  break;
                case 30:
                  // roll
                  var n = stack.pop();
                  var _j = stack.pop();
                  if (_j >= 0) {
                    while (_j > 0) {
                      var t = stack[n - 1];
                      for (var _i = n - 2; _i >= 0; _i--) {
                        stack[_i + 1] = stack[_i];
                      }
                      stack[0] = t;
                      _j--;
                    }
                  } else {
                    while (_j < 0) {
                      var t = stack[0];
                      for (var _i2 = 0; _i2 <= n; _i2++) {
                        stack[_i2] = stack[_i2 + 1];
                      }
                      stack[n - 1] = t;
                      _j++;
                    }
                  }
                  break;
                case 34:
                  // hflex
                  c1x = x + stack.shift();
                  c1y = y;
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  var c3x = c2x + stack.shift();
                  var c3y = c2y;
                  var c4x = c3x + stack.shift();
                  var c4y = c3y;
                  var c5x = c4x + stack.shift();
                  var c5y = c4y;
                  var c6x = c5x + stack.shift();
                  var c6y = c5y;
                  x = c6x;
                  y = c6y;
                  path.bezierCurveTo(c1x, c1y, c2x, c2y, c3x, c3y);
                  path.bezierCurveTo(c4x, c4y, c5x, c5y, c6x, c6y);
                  break;
                case 35:
                  // flex
                  var pts = [];
                  for (var _i3 = 0; _i3 <= 5; _i3++) {
                    x += stack.shift();
                    y += stack.shift();
                    pts.push(x, y);
                  }
                  path.bezierCurveTo.apply(path, pts.slice(0, 6));
                  path.bezierCurveTo.apply(path, pts.slice(6));
                  stack.shift(); // fd

                  break;
                case 36:
                  // hflex1
                  c1x = x + stack.shift();
                  c1y = y + stack.shift();
                  c2x = c1x + stack.shift();
                  c2y = c1y + stack.shift();
                  c3x = c2x + stack.shift();
                  c3y = c2y;
                  c4x = c3x + stack.shift();
                  c4y = c3y;
                  c5x = c4x + stack.shift();
                  c5y = c4y + stack.shift();
                  c6x = c5x + stack.shift();
                  c6y = c5y;
                  x = c6x;
                  y = c6y;
                  path.bezierCurveTo(c1x, c1y, c2x, c2y, c3x, c3y);
                  path.bezierCurveTo(c4x, c4y, c5x, c5y, c6x, c6y);
                  break;
                case 37:
                  // flex1
                  var startx = x;
                  var starty = y;
                  pts = [];
                  for (var _i4 = 0; _i4 <= 4; _i4++) {
                    x += stack.shift();
                    y += stack.shift();
                    pts.push(x, y);
                  }
                  if (Math.abs(x - startx) > Math.abs(y - starty)) {
                    // horizontal
                    x += stack.shift();
                    y = starty;
                  } else {
                    x = startx;
                    y += stack.shift();
                  }
                  pts.push(x, y);
                  path.bezierCurveTo.apply(path, pts.slice(0, 6));
                  path.bezierCurveTo.apply(path, pts.slice(6));
                  break;
                default:
                  throw new Error("Unknown op: 12 ".concat(op));
              }
              break;
            default:
              throw new Error("Unknown op: ".concat(op));
          }
        } else if (op < 247) {
          stack.push(op - 139);
        } else if (op < 251) {
          var b1 = stream.readUInt8();
          stack.push((op - 247) * 256 + b1 + 108);
        } else if (op < 255) {
          var b1 = stream.readUInt8();
          stack.push(-(op - 251) * 256 - b1 - 108);
        } else {
          stack.push(stream.readInt32BE() / 65536);
        }
      }
    };
    _parse();
    if (open) {
      path.closePath();
    }
    return path;
  };
  return CFFGlyph;
}(Glyph);
var SBIXImage = new r.Struct({
  originX: r.uint16,
  originY: r.uint16,
  type: new r.String(4),
  data: new r.Buffer(function (t) {
    return t.parent.buflen - t._currentOffset;
  })
});
/**
 * Represents a color (e.g. emoji) glyph in Apple's SBIX format.
 */
var SBIXGlyph = /*#__PURE__*/function (_TTFGlyph) {
  function SBIXGlyph() {
    return _TTFGlyph.apply(this, arguments) || this;
  }
  _inheritsLoose(SBIXGlyph, _TTFGlyph);
  var _proto36 = SBIXGlyph.prototype;
  /**
   * Returns an object representing a glyph image at the given point size.
   * The object has a data property with a Buffer containing the actual image data,
   * along with the image type, and origin.
   *
   * @param {number} size
   * @return {object}
   */
  _proto36.getImageForSize = function getImageForSize(size) {
    for (var i = 0; i < this._font.sbix.imageTables.length; i++) {
      var table = this._font.sbix.imageTables[i];
      if (table.ppem >= size) {
        break;
      }
    }
    var offsets = table.imageOffsets;
    var start = offsets[this.id];
    var end = offsets[this.id + 1];
    if (start === end) {
      return null;
    }
    this._font.stream.pos = start;
    return SBIXImage.decode(this._font.stream, {
      buflen: end - start
    });
  };
  _proto36.render = function render(ctx, size) {
    var img = this.getImageForSize(size);
    if (img != null) {
      var scale = size / this._font.unitsPerEm;
      ctx.image(img.data, {
        height: size,
        x: img.originX,
        y: (this.bbox.minY - img.originY) * scale
      });
    }
    if (this._font.sbix.flags.renderOutlines) {
      _TTFGlyph.prototype.render.call(this, ctx, size);
    }
  };
  return SBIXGlyph;
}(TTFGlyph);
var COLRLayer = function COLRLayer(glyph, color) {
  this.glyph = glyph;
  this.color = color;
};
/**
 * Represents a color (e.g. emoji) glyph in Microsoft's COLR format.
 * Each glyph in this format contain a list of colored layers, each
 * of which  is another vector glyph.
 */
var COLRGlyph = /*#__PURE__*/function (_Glyph3) {
  function COLRGlyph() {
    return _Glyph3.apply(this, arguments) || this;
  }
  _inheritsLoose(COLRGlyph, _Glyph3);
  var _proto37 = COLRGlyph.prototype;
  _proto37._getBBox = function _getBBox() {
    var bbox = new BBox();
    for (var i = 0; i < this.layers.length; i++) {
      var layer = this.layers[i];
      var b = layer.glyph.bbox;
      bbox.addPoint(b.minX, b.minY);
      bbox.addPoint(b.maxX, b.maxY);
    }
    return bbox;
  }
  /**
   * Returns an array of objects containing the glyph and color for
   * each layer in the composite color glyph.
   * @type {object[]}
   */;
  _proto37.render = function render(ctx, size) {
    for (var _iterator71 = _createForOfIteratorHelperLoose(this.layers), _step71; !(_step71 = _iterator71()).done;) {
      var _step71$value = _step71.value,
        glyph = _step71$value.glyph,
        color = _step71$value.color;
      ctx.fillColor([color.red, color.green, color.blue], color.alpha / 255 * 100);
      glyph.render(ctx, size);
    }
    return;
  };
  return _createClass(COLRGlyph, [{
    key: "layers",
    get: function get() {
      var cpal = this._font.CPAL;
      var colr = this._font.COLR;
      var low = 0;
      var high = colr.baseGlyphRecord.length - 1;
      while (low <= high) {
        var mid = low + high >> 1;
        var rec = colr.baseGlyphRecord[mid];
        if (this.id < rec.gid) {
          high = mid - 1;
        } else if (this.id > rec.gid) {
          low = mid + 1;
        } else {
          var baseLayer = rec;
          break;
        }
      } // if base glyph not found in COLR table,
      // default to normal glyph from glyf or CFF

      if (baseLayer == null) {
        var g = this._font._getBaseGlyph(this.id);
        var color = {
          red: 0,
          green: 0,
          blue: 0,
          alpha: 255
        };
        return [new COLRLayer(g, color)];
      } // otherwise, return an array of all the layers

      var layers = [];
      for (var i = baseLayer.firstLayerIndex; i < baseLayer.firstLayerIndex + baseLayer.numLayers; i++) {
        var rec = colr.layerRecords[i];
        var color = cpal.colorRecords[rec.paletteIndex];
        var g = this._font._getBaseGlyph(rec.gid);
        layers.push(new COLRLayer(g, color));
      }
      return layers;
    }
  }]);
}(Glyph);
var TUPLES_SHARE_POINT_NUMBERS = 0x8000;
var TUPLE_COUNT_MASK = 0x0fff;
var EMBEDDED_TUPLE_COORD = 0x8000;
var INTERMEDIATE_TUPLE = 0x4000;
var PRIVATE_POINT_NUMBERS = 0x2000;
var TUPLE_INDEX_MASK = 0x0fff;
var POINTS_ARE_WORDS = 0x80;
var POINT_RUN_COUNT_MASK = 0x7f;
var DELTAS_ARE_ZERO = 0x80;
var DELTAS_ARE_WORDS = 0x40;
var DELTA_RUN_COUNT_MASK = 0x3f;
/**
 * This class is transforms TrueType glyphs according to the data from
 * the Apple Advanced Typography variation tables (fvar, gvar, and avar).
 * These tables allow infinite adjustments to glyph weight, width, slant,
 * and optical size without the designer needing to specify every exact style.
 *
 * Apple's documentation for these tables is not great, so thanks to the
 * Freetype project for figuring much of this out.
 *
 * @private
 */
var GlyphVariationProcessor = /*#__PURE__*/function () {
  function GlyphVariationProcessor(font, coords) {
    this.font = font;
    this.normalizedCoords = this.normalizeCoords(coords);
    this.blendVectors = new Map();
  }
  var _proto38 = GlyphVariationProcessor.prototype;
  _proto38.normalizeCoords = function normalizeCoords(coords) {
    // the default mapping is linear along each axis, in two segments:
    // from the minValue to defaultValue, and from defaultValue to maxValue.
    var normalized = [];
    for (var i = 0; i < this.font.fvar.axis.length; i++) {
      var axis = this.font.fvar.axis[i];
      if (coords[i] < axis.defaultValue) {
        normalized.push((coords[i] - axis.defaultValue + Number.EPSILON) / (axis.defaultValue - axis.minValue + Number.EPSILON));
      } else {
        normalized.push((coords[i] - axis.defaultValue + Number.EPSILON) / (axis.maxValue - axis.defaultValue + Number.EPSILON));
      }
    } // if there is an avar table, the normalized value is calculated
    // by interpolating between the two nearest mapped values.

    if (this.font.avar) {
      for (var i = 0; i < this.font.avar.segment.length; i++) {
        var segment = this.font.avar.segment[i];
        for (var j = 0; j < segment.correspondence.length; j++) {
          var pair = segment.correspondence[j];
          if (j >= 1 && normalized[i] < pair.fromCoord) {
            var prev = segment.correspondence[j - 1];
            normalized[i] = ((normalized[i] - prev.fromCoord) * (pair.toCoord - prev.toCoord) + Number.EPSILON) / (pair.fromCoord - prev.fromCoord + Number.EPSILON) + prev.toCoord;
            break;
          }
        }
      }
    }
    return normalized;
  };
  _proto38.transformPoints = function transformPoints(gid, glyphPoints) {
    if (!this.font.fvar || !this.font.gvar) {
      return;
    }
    var gvar = this.font.gvar;
    if (gid >= gvar.glyphCount) {
      return;
    }
    var offset = gvar.offsets[gid];
    if (offset === gvar.offsets[gid + 1]) {
      return;
    } // Read the gvar data for this glyph

    var stream = this.font.stream;
    stream.pos = offset;
    if (stream.pos >= stream.length) {
      return;
    }
    var tupleCount = stream.readUInt16BE();
    var offsetToData = offset + stream.readUInt16BE();
    if (tupleCount & TUPLES_SHARE_POINT_NUMBERS) {
      var here = stream.pos;
      stream.pos = offsetToData;
      var sharedPoints = this.decodePoints();
      offsetToData = stream.pos;
      stream.pos = here;
    }
    var origPoints = glyphPoints.map(function (pt) {
      return pt.copy();
    });
    tupleCount &= TUPLE_COUNT_MASK;
    for (var i = 0; i < tupleCount; i++) {
      var tupleDataSize = stream.readUInt16BE();
      var tupleIndex = stream.readUInt16BE();
      if (tupleIndex & EMBEDDED_TUPLE_COORD) {
        var tupleCoords = [];
        for (var a = 0; a < gvar.axisCount; a++) {
          tupleCoords.push(stream.readInt16BE() / 16384);
        }
      } else {
        if ((tupleIndex & TUPLE_INDEX_MASK) >= gvar.globalCoordCount) {
          throw new Error('Invalid gvar table');
        }
        var tupleCoords = gvar.globalCoords[tupleIndex & TUPLE_INDEX_MASK];
      }
      if (tupleIndex & INTERMEDIATE_TUPLE) {
        var startCoords = [];
        for (var _a = 0; _a < gvar.axisCount; _a++) {
          startCoords.push(stream.readInt16BE() / 16384);
        }
        var endCoords = [];
        for (var _a2 = 0; _a2 < gvar.axisCount; _a2++) {
          endCoords.push(stream.readInt16BE() / 16384);
        }
      } // Get the factor at which to apply this tuple

      var factor = this.tupleFactor(tupleIndex, tupleCoords, startCoords, endCoords);
      if (factor === 0) {
        offsetToData += tupleDataSize;
        continue;
      }
      var here = stream.pos;
      stream.pos = offsetToData;
      if (tupleIndex & PRIVATE_POINT_NUMBERS) {
        var points = this.decodePoints();
      } else {
        var points = sharedPoints;
      } // points.length = 0 means there are deltas for all points

      var nPoints = points.length === 0 ? glyphPoints.length : points.length;
      var xDeltas = this.decodeDeltas(nPoints);
      var yDeltas = this.decodeDeltas(nPoints);
      if (points.length === 0) {
        // all points
        for (var _i = 0; _i < glyphPoints.length; _i++) {
          var point = glyphPoints[_i];
          point.x += Math.round(xDeltas[_i] * factor);
          point.y += Math.round(yDeltas[_i] * factor);
        }
      } else {
        var outPoints = origPoints.map(function (pt) {
          return pt.copy();
        });
        var hasDelta = glyphPoints.map(function () {
          return false;
        });
        for (var _i2 = 0; _i2 < points.length; _i2++) {
          var idx = points[_i2];
          if (idx < glyphPoints.length) {
            var _point = outPoints[idx];
            hasDelta[idx] = true;
            _point.x += Math.round(xDeltas[_i2] * factor);
            _point.y += Math.round(yDeltas[_i2] * factor);
          }
        }
        this.interpolateMissingDeltas(outPoints, origPoints, hasDelta);
        for (var _i3 = 0; _i3 < glyphPoints.length; _i3++) {
          var deltaX = outPoints[_i3].x - origPoints[_i3].x;
          var deltaY = outPoints[_i3].y - origPoints[_i3].y;
          glyphPoints[_i3].x += deltaX;
          glyphPoints[_i3].y += deltaY;
        }
      }
      offsetToData += tupleDataSize;
      stream.pos = here;
    }
  };
  _proto38.decodePoints = function decodePoints() {
    var stream = this.font.stream;
    var count = stream.readUInt8();
    if (count & POINTS_ARE_WORDS) {
      count = (count & POINT_RUN_COUNT_MASK) << 8 | stream.readUInt8();
    }
    var points = new Uint16Array(count);
    var i = 0;
    var point = 0;
    while (i < count) {
      var run = stream.readUInt8();
      var runCount = (run & POINT_RUN_COUNT_MASK) + 1;
      var fn = run & POINTS_ARE_WORDS ? stream.readUInt16 : stream.readUInt8;
      for (var j = 0; j < runCount && i < count; j++) {
        point += fn.call(stream);
        points[i++] = point;
      }
    }
    return points;
  };
  _proto38.decodeDeltas = function decodeDeltas(count) {
    var stream = this.font.stream;
    var i = 0;
    var deltas = new Int16Array(count);
    while (i < count) {
      var run = stream.readUInt8();
      var runCount = (run & DELTA_RUN_COUNT_MASK) + 1;
      if (run & DELTAS_ARE_ZERO) {
        i += runCount;
      } else {
        var fn = run & DELTAS_ARE_WORDS ? stream.readInt16BE : stream.readInt8;
        for (var j = 0; j < runCount && i < count; j++) {
          deltas[i++] = fn.call(stream);
        }
      }
    }
    return deltas;
  };
  _proto38.tupleFactor = function tupleFactor(tupleIndex, tupleCoords, startCoords, endCoords) {
    var normalized = this.normalizedCoords;
    var gvar = this.font.gvar;
    var factor = 1;
    for (var i = 0; i < gvar.axisCount; i++) {
      if (tupleCoords[i] === 0) {
        continue;
      }
      if (normalized[i] === 0) {
        return 0;
      }
      if ((tupleIndex & INTERMEDIATE_TUPLE) === 0) {
        if (normalized[i] < Math.min(0, tupleCoords[i]) || normalized[i] > Math.max(0, tupleCoords[i])) {
          return 0;
        }
        factor = (factor * normalized[i] + Number.EPSILON) / (tupleCoords[i] + Number.EPSILON);
      } else {
        if (normalized[i] < startCoords[i] || normalized[i] > endCoords[i]) {
          return 0;
        } else if (normalized[i] < tupleCoords[i]) {
          factor = factor * (normalized[i] - startCoords[i] + Number.EPSILON) / (tupleCoords[i] - startCoords[i] + Number.EPSILON);
        } else {
          factor = factor * (endCoords[i] - normalized[i] + Number.EPSILON) / (endCoords[i] - tupleCoords[i] + Number.EPSILON);
        }
      }
    }
    return factor;
  } // Interpolates points without delta values.
  // Needed for the Ø and Q glyphs in Skia.
  // Algorithm from Freetype.
  ;
  _proto38.interpolateMissingDeltas = function interpolateMissingDeltas(points, inPoints, hasDelta) {
    if (points.length === 0) {
      return;
    }
    var point = 0;
    while (point < points.length) {
      var firstPoint = point; // find the end point of the contour

      var endPoint = point;
      var pt = points[endPoint];
      while (!pt.endContour) {
        pt = points[++endPoint];
      } // find the first point that has a delta

      while (point <= endPoint && !hasDelta[point]) {
        point++;
      }
      if (point > endPoint) {
        continue;
      }
      var firstDelta = point;
      var curDelta = point;
      point++;
      while (point <= endPoint) {
        // find the next point with a delta, and interpolate intermediate points
        if (hasDelta[point]) {
          this.deltaInterpolate(curDelta + 1, point - 1, curDelta, point, inPoints, points);
          curDelta = point;
        }
        point++;
      } // shift contour if we only have a single delta

      if (curDelta === firstDelta) {
        this.deltaShift(firstPoint, endPoint, curDelta, inPoints, points);
      } else {
        // otherwise, handle the remaining points at the end and beginning of the contour
        this.deltaInterpolate(curDelta + 1, endPoint, curDelta, firstDelta, inPoints, points);
        if (firstDelta > 0) {
          this.deltaInterpolate(firstPoint, firstDelta - 1, curDelta, firstDelta, inPoints, points);
        }
      }
      point = endPoint + 1;
    }
  };
  _proto38.deltaInterpolate = function deltaInterpolate(p1, p2, ref1, ref2, inPoints, outPoints) {
    if (p1 > p2) {
      return;
    }
    var iterable = ['x', 'y'];
    for (var i = 0; i < iterable.length; i++) {
      var k = iterable[i];
      if (inPoints[ref1][k] > inPoints[ref2][k]) {
        var p = ref1;
        ref1 = ref2;
        ref2 = p;
      }
      var in1 = inPoints[ref1][k];
      var in2 = inPoints[ref2][k];
      var out1 = outPoints[ref1][k];
      var out2 = outPoints[ref2][k]; // If the reference points have the same coordinate but different
      // delta, inferred delta is zero.  Otherwise interpolate.

      if (in1 !== in2 || out1 === out2) {
        var scale = in1 === in2 ? 0 : (out2 - out1) / (in2 - in1);
        for (var _p = p1; _p <= p2; _p++) {
          var out = inPoints[_p][k];
          if (out <= in1) {
            out += out1 - in1;
          } else if (out >= in2) {
            out += out2 - in2;
          } else {
            out = out1 + (out - in1) * scale;
          }
          outPoints[_p][k] = out;
        }
      }
    }
  };
  _proto38.deltaShift = function deltaShift(p1, p2, ref, inPoints, outPoints) {
    var deltaX = outPoints[ref].x - inPoints[ref].x;
    var deltaY = outPoints[ref].y - inPoints[ref].y;
    if (deltaX === 0 && deltaY === 0) {
      return;
    }
    for (var p = p1; p <= p2; p++) {
      if (p !== ref) {
        outPoints[p].x += deltaX;
        outPoints[p].y += deltaY;
      }
    }
  };
  _proto38.getAdvanceAdjustment = function getAdvanceAdjustment(gid, table) {
    var outerIndex, innerIndex;
    if (table.advanceWidthMapping) {
      var idx = gid;
      if (idx >= table.advanceWidthMapping.mapCount) {
        idx = table.advanceWidthMapping.mapCount - 1;
      }
      var entryFormat = table.advanceWidthMapping.entryFormat;
      var _table$advanceWidthMa = table.advanceWidthMapping.mapData[idx];
      outerIndex = _table$advanceWidthMa.outerIndex;
      innerIndex = _table$advanceWidthMa.innerIndex;
    } else {
      outerIndex = 0;
      innerIndex = gid;
    }
    return this.getDelta(table.itemVariationStore, outerIndex, innerIndex);
  } // See pseudo code from `Font Variations Overview'
  // in the OpenType specification.
  ;
  _proto38.getDelta = function getDelta(itemStore, outerIndex, innerIndex) {
    if (outerIndex >= itemStore.itemVariationData.length) {
      return 0;
    }
    var varData = itemStore.itemVariationData[outerIndex];
    if (innerIndex >= varData.deltaSets.length) {
      return 0;
    }
    var deltaSet = varData.deltaSets[innerIndex];
    var blendVector = this.getBlendVector(itemStore, outerIndex);
    var netAdjustment = 0;
    for (var master = 0; master < varData.regionIndexCount; master++) {
      netAdjustment += deltaSet.deltas[master] * blendVector[master];
    }
    return netAdjustment;
  };
  _proto38.getBlendVector = function getBlendVector(itemStore, outerIndex) {
    var varData = itemStore.itemVariationData[outerIndex];
    if (this.blendVectors.has(varData)) {
      return this.blendVectors.get(varData);
    }
    var normalizedCoords = this.normalizedCoords;
    var blendVector = []; // outer loop steps through master designs to be blended

    for (var master = 0; master < varData.regionIndexCount; master++) {
      var scalar = 1;
      var regionIndex = varData.regionIndexes[master];
      var axes = itemStore.variationRegionList.variationRegions[regionIndex]; // inner loop steps through axes in this region

      for (var j = 0; j < axes.length; j++) {
        var axis = axes[j];
        var axisScalar = void 0; // compute the scalar contribution of this axis
        // ignore invalid ranges

        if (axis.startCoord > axis.peakCoord || axis.peakCoord > axis.endCoord) {
          axisScalar = 1;
        } else if (axis.startCoord < 0 && axis.endCoord > 0 && axis.peakCoord !== 0) {
          axisScalar = 1; // peak of 0 means ignore this axis
        } else if (axis.peakCoord === 0) {
          axisScalar = 1; // ignore this region if coords are out of range
        } else if (normalizedCoords[j] < axis.startCoord || normalizedCoords[j] > axis.endCoord) {
          axisScalar = 0; // calculate a proportional factor
        } else {
          if (normalizedCoords[j] === axis.peakCoord) {
            axisScalar = 1;
          } else if (normalizedCoords[j] < axis.peakCoord) {
            axisScalar = (normalizedCoords[j] - axis.startCoord + Number.EPSILON) / (axis.peakCoord - axis.startCoord + Number.EPSILON);
          } else {
            axisScalar = (axis.endCoord - normalizedCoords[j] + Number.EPSILON) / (axis.endCoord - axis.peakCoord + Number.EPSILON);
          }
        } // take product of all the axis scalars

        scalar *= axisScalar;
      }
      blendVector[master] = scalar;
    }
    this.blendVectors.set(varData, blendVector);
    return blendVector;
  };
  return GlyphVariationProcessor;
}();
var resolved = Promise.resolve();
var Subset = /*#__PURE__*/function () {
  function Subset(font) {
    this.font = font;
    this.glyphs = [];
    this.mapping = {}; // always include the missing glyph

    this.includeGlyph(0);
  }
  var _proto39 = Subset.prototype;
  _proto39.includeGlyph = function includeGlyph(glyph) {
    if (typeof glyph === 'object') {
      glyph = glyph.id;
    }
    if (this.mapping[glyph] == null) {
      this.glyphs.push(glyph);
      this.mapping[glyph] = this.glyphs.length - 1;
    }
    return this.mapping[glyph];
  };
  _proto39.encodeStream = function encodeStream() {
    var _this11 = this;
    var s = new r.EncodeStream();
    resolved.then(function () {
      _this11.encode(s);
      return s.end();
    });
    return s;
  };
  return Subset;
}();
var ON_CURVE$1 = 1 << 0;
var X_SHORT_VECTOR$1 = 1 << 1;
var Y_SHORT_VECTOR$1 = 1 << 2;
var REPEAT$1 = 1 << 3;
var SAME_X$1 = 1 << 4;
var SAME_Y$1 = 1 << 5;
var Point$1 = /*#__PURE__*/function () {
  function Point$1() {}
  Point$1.size = function size(val) {
    return val >= 0 && val <= 255 ? 1 : 2;
  };
  Point$1.encode = function encode(stream, value) {
    if (value >= 0 && value <= 255) {
      stream.writeUInt8(value);
    } else {
      stream.writeInt16BE(value);
    }
  };
  return Point$1;
}();
var Glyf = new r.Struct({
  numberOfContours: r.int16,
  // if negative, this is a composite glyph
  xMin: r.int16,
  yMin: r.int16,
  xMax: r.int16,
  yMax: r.int16,
  endPtsOfContours: new r.Array(r.uint16, 'numberOfContours'),
  instructions: new r.Array(r.uint8, r.uint16),
  flags: new r.Array(r.uint8, 0),
  xPoints: new r.Array(Point$1, 0),
  yPoints: new r.Array(Point$1, 0)
});
/**
 * Encodes TrueType glyph outlines
 */
var TTFGlyphEncoder = /*#__PURE__*/function () {
  function TTFGlyphEncoder() {}
  var _proto40 = TTFGlyphEncoder.prototype;
  _proto40.encodeSimple = function encodeSimple(path, instructions) {
    if (instructions === void 0) {
      instructions = [];
    }
    var endPtsOfContours = [];
    var xPoints = [];
    var yPoints = [];
    var flags = [];
    var same = 0;
    var lastX = 0,
      lastY = 0,
      lastFlag = 0;
    var pointCount = 0;
    for (var i = 0; i < path.commands.length; i++) {
      var c = path.commands[i];
      for (var j = 0; j < c.args.length; j += 2) {
        var x = c.args[j];
        var y = c.args[j + 1];
        var flag = 0; // If the ending point of a quadratic curve is the midpoint
        // between the control point and the control point of the next
        // quadratic curve, we can omit the ending point.

        if (c.command === 'quadraticCurveTo' && j === 2) {
          var next = path.commands[i + 1];
          if (next && next.command === 'quadraticCurveTo') {
            var midX = (lastX + next.args[0]) / 2;
            var midY = (lastY + next.args[1]) / 2;
            if (x === midX && y === midY) {
              continue;
            }
          }
        } // All points except control points are on curve.

        if (!(c.command === 'quadraticCurveTo' && j === 0)) {
          flag |= ON_CURVE$1;
        }
        flag = this._encodePoint(x, lastX, xPoints, flag, X_SHORT_VECTOR$1, SAME_X$1);
        flag = this._encodePoint(y, lastY, yPoints, flag, Y_SHORT_VECTOR$1, SAME_Y$1);
        if (flag === lastFlag && same < 255) {
          flags[flags.length - 1] |= REPEAT$1;
          same++;
        } else {
          if (same > 0) {
            flags.push(same);
            same = 0;
          }
          flags.push(flag);
          lastFlag = flag;
        }
        lastX = x;
        lastY = y;
        pointCount++;
      }
      if (c.command === 'closePath') {
        endPtsOfContours.push(pointCount - 1);
      }
    } // Close the path if the last command didn't already

    if (path.commands.length > 1 && path.commands[path.commands.length - 1].command !== 'closePath') {
      endPtsOfContours.push(pointCount - 1);
    }
    var bbox = path.bbox;
    var glyf = {
      numberOfContours: endPtsOfContours.length,
      xMin: bbox.minX,
      yMin: bbox.minY,
      xMax: bbox.maxX,
      yMax: bbox.maxY,
      endPtsOfContours: endPtsOfContours,
      instructions: instructions,
      flags: flags,
      xPoints: xPoints,
      yPoints: yPoints
    };
    var size = Glyf.size(glyf);
    var tail = 4 - size % 4;
    var stream = new r.EncodeStream(size + tail);
    Glyf.encode(stream, glyf); // Align to 4-byte length

    if (tail !== 0) {
      stream.fill(0, tail);
    }
    return stream.buffer;
  };
  _proto40._encodePoint = function _encodePoint(value, last, points, flag, shortFlag, sameFlag) {
    var diff = value - last;
    if (value === last) {
      flag |= sameFlag;
    } else {
      if (-255 <= diff && diff <= 255) {
        flag |= shortFlag;
        if (diff < 0) {
          diff = -diff;
        } else {
          flag |= sameFlag;
        }
      }
      points.push(diff);
    }
    return flag;
  };
  return TTFGlyphEncoder;
}();
var TTFSubset = /*#__PURE__*/function (_Subset) {
  function TTFSubset(font) {
    var _this12;
    _this12 = _Subset.call(this, font) || this;
    _this12.glyphEncoder = new TTFGlyphEncoder();
    return _this12;
  }
  _inheritsLoose(TTFSubset, _Subset);
  var _proto41 = TTFSubset.prototype;
  _proto41._addGlyph = function _addGlyph(gid) {
    var glyph = this.font.getGlyph(gid);
    var glyf = glyph._decode(); // get the offset to the glyph from the loca table

    var curOffset = this.font.loca.offsets[gid];
    var nextOffset = this.font.loca.offsets[gid + 1];
    var stream = this.font._getTableStream('glyf');
    stream.pos += curOffset;
    var buffer = stream.readBuffer(nextOffset - curOffset); // if it is a compound glyph, include its components

    if (glyf && glyf.numberOfContours < 0) {
      buffer = Buffer.from(buffer);
      for (var _iterator72 = _createForOfIteratorHelperLoose(glyf.components), _step72; !(_step72 = _iterator72()).done;) {
        var component = _step72.value;
        gid = this.includeGlyph(component.glyphID);
        buffer.writeUInt16BE(gid, component.pos);
      }
    } else if (glyf && this.font._variationProcessor) {
      // If this is a TrueType variation glyph, re-encode the path
      buffer = this.glyphEncoder.encodeSimple(glyph.path, glyf.instructions);
    }
    this.glyf.push(buffer);
    this.loca.offsets.push(this.offset);
    this.hmtx.metrics.push({
      advance: glyph.advanceWidth,
      bearing: glyph._getMetrics().leftBearing
    });
    this.offset += buffer.length;
    return this.glyf.length - 1;
  };
  _proto41.encode = function encode(stream) {
    // tables required by PDF spec:
    //   head, hhea, loca, maxp, cvt , prep, glyf, hmtx, fpgm
    //
    // additional tables required for standalone fonts:
    //   name, cmap, OS/2, post
    this.glyf = [];
    this.offset = 0;
    this.loca = {
      offsets: [],
      version: this.font.loca.version
    };
    this.hmtx = {
      metrics: [],
      bearings: []
    }; // include all the glyphs
    // not using a for loop because we need to support adding more
    // glyphs to the array as we go, and CoffeeScript caches the length.

    var i = 0;
    while (i < this.glyphs.length) {
      this._addGlyph(this.glyphs[i++]);
    }
    var maxp = cloneDeep(this.font.maxp);
    maxp.numGlyphs = this.glyf.length;
    this.loca.offsets.push(this.offset);
    var head = cloneDeep(this.font.head);
    head.indexToLocFormat = this.loca.version;
    var hhea = cloneDeep(this.font.hhea);
    hhea.numberOfMetrics = this.hmtx.metrics.length; // map = []
    // for index in [0...256]
    //     if index < @numGlyphs
    //         map[index] = index
    //     else
    //         map[index] = 0
    //
    // cmapTable =
    //     version: 0
    //     length: 262
    //     language: 0
    //     codeMap: map
    //
    // cmap =
    //     version: 0
    //     numSubtables: 1
    //     tables: [
    //         platformID: 1
    //         encodingID: 0
    //         table: cmapTable
    //     ]
    // TODO: subset prep, cvt, fpgm?

    Directory.encode(stream, {
      tables: {
        head: head,
        hhea: hhea,
        loca: this.loca,
        maxp: maxp,
        'cvt ': this.font['cvt '],
        prep: this.font.prep,
        glyf: this.glyf,
        hmtx: this.hmtx,
        fpgm: this.font.fpgm // name: clone @font.name
        // 'OS/2': clone @font['OS/2']
        // post: clone @font.post
        // cmap: cmap
      }
    });
  };
  return TTFSubset;
}(Subset);
var CFFSubset = /*#__PURE__*/function (_Subset2) {
  function CFFSubset(font) {
    var _this13;
    _this13 = _Subset2.call(this, font) || this;
    _this13.cff = _this13.font['CFF '];
    if (!_this13.cff) {
      throw new Error('Not a CFF Font');
    }
    return _this13;
  }
  _inheritsLoose(CFFSubset, _Subset2);
  var _proto42 = CFFSubset.prototype;
  _proto42.subsetCharstrings = function subsetCharstrings() {
    this.charstrings = [];
    var gsubrs = {};
    for (var _iterator73 = _createForOfIteratorHelperLoose(this.glyphs), _step73; !(_step73 = _iterator73()).done;) {
      var gid = _step73.value;
      this.charstrings.push(this.cff.getCharString(gid));
      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed

      for (var subr in glyph._usedGsubrs) {
        gsubrs[subr] = true;
      }
    }
    this.gsubrs = this.subsetSubrs(this.cff.globalSubrIndex, gsubrs);
  };
  _proto42.subsetSubrs = function subsetSubrs(subrs, used) {
    var res = [];
    for (var i = 0; i < subrs.length; i++) {
      var subr = subrs[i];
      if (used[i]) {
        this.cff.stream.pos = subr.offset;
        res.push(this.cff.stream.readBuffer(subr.length));
      } else {
        res.push(Buffer.from([11])); // return
      }
    }
    return res;
  };
  _proto42.subsetFontdict = function subsetFontdict(topDict) {
    topDict.FDArray = [];
    topDict.FDSelect = {
      version: 0,
      fds: []
    };
    var used_fds = {};
    var used_subrs = [];
    var fd_select = {};
    for (var _iterator74 = _createForOfIteratorHelperLoose(this.glyphs), _step74; !(_step74 = _iterator74()).done;) {
      var gid = _step74.value;
      var fd = this.cff.fdForGlyph(gid);
      if (fd == null) {
        continue;
      }
      if (!used_fds[fd]) {
        topDict.FDArray.push(Object.assign({}, this.cff.topDict.FDArray[fd]));
        used_subrs.push({});
        fd_select[fd] = topDict.FDArray.length - 1;
      }
      used_fds[fd] = true;
      topDict.FDSelect.fds.push(fd_select[fd]);
      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed

      for (var subr in glyph._usedSubrs) {
        used_subrs[fd_select[fd]][subr] = true;
      }
    }
    for (var i = 0; i < topDict.FDArray.length; i++) {
      var dict = topDict.FDArray[i];
      delete dict.FontName;
      if (dict.Private && dict.Private.Subrs) {
        dict.Private = Object.assign({}, dict.Private);
        dict.Private.Subrs = this.subsetSubrs(dict.Private.Subrs, used_subrs[i]);
      }
    }
    return;
  };
  _proto42.createCIDFontdict = function createCIDFontdict(topDict) {
    var used_subrs = {};
    for (var _iterator75 = _createForOfIteratorHelperLoose(this.glyphs), _step75; !(_step75 = _iterator75()).done;) {
      var gid = _step75.value;
      var glyph = this.font.getGlyph(gid);
      var path = glyph.path; // this causes the glyph to be parsed

      for (var subr in glyph._usedSubrs) {
        used_subrs[subr] = true;
      }
    }
    var privateDict = Object.assign({}, this.cff.topDict.Private);
    if (this.cff.topDict.Private && this.cff.topDict.Private.Subrs) {
      privateDict.Subrs = this.subsetSubrs(this.cff.topDict.Private.Subrs, used_subrs);
    }
    topDict.FDArray = [{
      Private: privateDict
    }];
    return topDict.FDSelect = {
      version: 3,
      nRanges: 1,
      ranges: [{
        first: 0,
        fd: 0
      }],
      sentinel: this.charstrings.length
    };
  };
  _proto42.addString = function addString(string) {
    if (!string) {
      return null;
    }
    if (!this.strings) {
      this.strings = [];
    }
    this.strings.push(string);
    return standardStrings.length + this.strings.length - 1;
  };
  _proto42.encode = function encode(stream) {
    this.subsetCharstrings();
    var charset = {
      version: this.charstrings.length > 255 ? 2 : 1,
      ranges: [{
        first: 1,
        nLeft: this.charstrings.length - 2
      }]
    };
    var topDict = Object.assign({}, this.cff.topDict);
    topDict.Private = null;
    topDict.charset = charset;
    topDict.Encoding = null;
    topDict.CharStrings = this.charstrings;
    for (var _i24 = 0, _arr2 = ['version', 'Notice', 'Copyright', 'FullName', 'FamilyName', 'Weight', 'PostScript', 'BaseFontName', 'FontName']; _i24 < _arr2.length; _i24++) {
      var key = _arr2[_i24];
      topDict[key] = this.addString(this.cff.string(topDict[key]));
    }
    topDict.ROS = [this.addString('Adobe'), this.addString('Identity'), 0];
    topDict.CIDCount = this.charstrings.length;
    if (this.cff.isCIDFont) {
      this.subsetFontdict(topDict);
    } else {
      this.createCIDFontdict(topDict);
    }
    var top = {
      version: 1,
      hdrSize: this.cff.hdrSize,
      offSize: 4,
      header: this.cff.header,
      nameIndex: [this.cff.postscriptName],
      topDictIndex: [topDict],
      stringIndex: this.strings,
      globalSubrIndex: this.gsubrs
    };
    CFFTop.encode(stream, top);
  };
  return CFFSubset;
}(Subset);
var _class$4;
/**
 * This is the base class for all SFNT-based font formats in fontkit.
 * It supports TrueType, and PostScript glyphs, and several color glyph formats.
 */

var TTFFont = (_class$4 = /*#__PURE__*/function () {
  function TTFFont(stream, variationCoords) {
    if (variationCoords === void 0) {
      variationCoords = null;
    }
    this.defaultLanguage = null;
    this.stream = stream;
    this.variationCoords = variationCoords;
    this._directoryPos = this.stream.pos;
    this._tables = {};
    this._glyphs = {};
    this._decodeDirectory(); // define properties for each table to lazily parse

    for (var tag in this.directory.tables) {
      var table = this.directory.tables[tag];
      if (tables[tag] && table.length > 0) {
        Object.defineProperty(this, tag, {
          get: this._getTable.bind(this, table)
        });
      }
    }
  }
  TTFFont.probe = function probe(buffer) {
    var format = buffer.toString('ascii', 0, 4);
    return format === 'true' || format === 'OTTO' || format === String.fromCharCode(0, 1, 0, 0);
  };
  var _proto43 = TTFFont.prototype;
  _proto43.setDefaultLanguage = function setDefaultLanguage(lang) {
    if (lang === void 0) {
      lang = null;
    }
    this.defaultLanguage = lang;
  };
  _proto43._getTable = function _getTable(table) {
    if (!(table.tag in this._tables)) {
      try {
        this._tables[table.tag] = this._decodeTable(table);
      } catch (e) {
        if (fontkit.logErrors) {
          console.error("Error decoding table ".concat(table.tag));
          console.error(e.stack);
        }
      }
    }
    return this._tables[table.tag];
  };
  _proto43._getTableStream = function _getTableStream(tag) {
    var table = this.directory.tables[tag];
    if (table) {
      this.stream.pos = table.offset;
      return this.stream;
    }
    return null;
  };
  _proto43._decodeDirectory = function _decodeDirectory() {
    return this.directory = Directory.decode(this.stream, {
      _startOffset: 0
    });
  };
  _proto43._decodeTable = function _decodeTable(table) {
    var pos = this.stream.pos;
    var stream = this._getTableStream(table.tag);
    var result = tables[table.tag].decode(stream, this, table.length);
    this.stream.pos = pos;
    return result;
  }
  /**
   * Gets a string from the font's `name` table
   * `lang` is a BCP-47 language code.
   * @return {string}
   */;
  _proto43.getName = function getName(key, lang) {
    if (lang === void 0) {
      lang = this.defaultLanguage || fontkit.defaultLanguage;
    }
    var record = this.name && this.name.records[key];
    if (record) {
      // Attempt to retrieve the entry, depending on which translation is available:
      return record[lang] || record[this.defaultLanguage] || record[fontkit.defaultLanguage] || record['en'] || record[Object.keys(record)[0]] // Seriously, ANY language would be fine
      || null;
    }
    return null;
  }
  /**
   * The unique PostScript name for this font, e.g. "Helvetica-Bold"
   * @type {string}
   */;
  /**
   * Returns whether there is glyph in the font for the given unicode code point.
   *
   * @param {number} codePoint
   * @return {boolean}
   */
  _proto43.hasGlyphForCodePoint = function hasGlyphForCodePoint(codePoint) {
    return !!this._cmapProcessor.lookup(codePoint);
  }
  /**
   * Maps a single unicode code point to a Glyph object.
   * Does not perform any advanced substitutions (there is no context to do so).
   *
   * @param {number} codePoint
   * @return {Glyph}
   */;
  _proto43.glyphForCodePoint = function glyphForCodePoint(codePoint) {
    return this.getGlyph(this._cmapProcessor.lookup(codePoint), [codePoint]);
  }
  /**
   * Returns an array of Glyph objects for the given string.
   * This is only a one-to-one mapping from characters to glyphs.
   * For most uses, you should use font.layout (described below), which
   * provides a much more advanced mapping supporting AAT and OpenType shaping.
   *
   * @param {string} string
   * @return {Glyph[]}
   */;
  _proto43.glyphsForString = function glyphsForString(string) {
    var glyphs = [];
    var len = string.length;
    var idx = 0;
    var last = -1;
    var state = -1;
    while (idx <= len) {
      var code = 0;
      var nextState = 0;
      if (idx < len) {
        // Decode the next codepoint from UTF 16
        code = string.charCodeAt(idx++);
        if (0xd800 <= code && code <= 0xdbff && idx < len) {
          var next = string.charCodeAt(idx);
          if (0xdc00 <= next && next <= 0xdfff) {
            idx++;
            code = ((code & 0x3ff) << 10) + (next & 0x3ff) + 0x10000;
          }
        } // Compute the next state: 1 if the next codepoint is a variation selector, 0 otherwise.

        nextState = 0xfe00 <= code && code <= 0xfe0f || 0xe0100 <= code && code <= 0xe01ef ? 1 : 0;
      } else {
        idx++;
      }
      if (state === 0 && nextState === 1) {
        // Variation selector following normal codepoint.
        glyphs.push(this.getGlyph(this._cmapProcessor.lookup(last, code), [last, code]));
      } else if (state === 0 && nextState === 0) {
        // Normal codepoint following normal codepoint.
        glyphs.push(this.glyphForCodePoint(last));
      }
      last = code;
      state = nextState;
    }
    return glyphs;
  };
  /**
   * Returns a GlyphRun object, which includes an array of Glyphs and GlyphPositions for the given string.
   *
   * @param {string} string
   * @param {string[]} [userFeatures]
   * @param {string} [script]
   * @param {string} [language]
   * @param {string} [direction]
   * @return {GlyphRun}
   */
  _proto43.layout = function layout(string, userFeatures, script, language, direction) {
    return this._layoutEngine.layout(string, userFeatures, script, language, direction);
  }
  /**
   * Returns an array of strings that map to the given glyph id.
   * @param {number} gid - glyph id
   */;
  _proto43.stringsForGlyph = function stringsForGlyph(gid) {
    return this._layoutEngine.stringsForGlyph(gid);
  }
  /**
   * An array of all [OpenType feature tags](https://www.microsoft.com/typography/otspec/featuretags.htm)
   * (or mapped AAT tags) supported by the font.
   * The features parameter is an array of OpenType feature tags to be applied in addition to the default set.
   * If this is an AAT font, the OpenType feature tags are mapped to AAT features.
   *
   * @type {string[]}
   */;
  _proto43.getAvailableFeatures = function getAvailableFeatures(script, language) {
    return this._layoutEngine.getAvailableFeatures(script, language);
  };
  _proto43._getBaseGlyph = function _getBaseGlyph(glyph, characters) {
    if (characters === void 0) {
      characters = [];
    }
    if (!this._glyphs[glyph]) {
      if (this.directory.tables.glyf) {
        this._glyphs[glyph] = new TTFGlyph(glyph, characters, this);
      } else if (this.directory.tables['CFF '] || this.directory.tables.CFF2) {
        this._glyphs[glyph] = new CFFGlyph(glyph, characters, this);
      }
    }
    return this._glyphs[glyph] || null;
  }
  /**
   * Returns a glyph object for the given glyph id.
   * You can pass the array of code points this glyph represents for
   * your use later, and it will be stored in the glyph object.
   *
   * @param {number} glyph
   * @param {number[]} characters
   * @return {Glyph}
   */;
  _proto43.getGlyph = function getGlyph(glyph, characters) {
    if (characters === void 0) {
      characters = [];
    }
    if (!this._glyphs[glyph]) {
      if (this.directory.tables.sbix) {
        this._glyphs[glyph] = new SBIXGlyph(glyph, characters, this);
      } else if (this.directory.tables.COLR && this.directory.tables.CPAL) {
        this._glyphs[glyph] = new COLRGlyph(glyph, characters, this);
      } else {
        this._getBaseGlyph(glyph, characters);
      }
    }
    return this._glyphs[glyph] || null;
  }
  /**
   * Returns a Subset for this font.
   * @return {Subset}
   */;
  _proto43.createSubset = function createSubset() {
    if (this.directory.tables['CFF ']) {
      return new CFFSubset(this);
    }
    return new TTFSubset(this);
  }
  /**
   * Returns an object describing the available variation axes
   * that this font supports. Keys are setting tags, and values
   * contain the axis name, range, and default value.
   *
   * @type {object}
   */;
  /**
   * Returns a new font with the given variation settings applied.
   * Settings can either be an instance name, or an object containing
   * variation tags as specified by the `variationAxes` property.
   *
   * @param {object} settings
   * @return {TTFFont}
   */
  _proto43.getVariation = function getVariation(settings) {
    if (!(this.directory.tables.fvar && (this.directory.tables.gvar && this.directory.tables.glyf || this.directory.tables.CFF2))) {
      throw new Error('Variations require a font with the fvar, gvar and glyf, or CFF2 tables.');
    }
    if (typeof settings === 'string') {
      settings = this.namedVariations[settings];
    }
    if (typeof settings !== 'object') {
      throw new Error('Variation settings must be either a variation name or settings object.');
    } // normalize the coordinates

    var coords = this.fvar.axis.map(function (axis, i) {
      var axisTag = axis.axisTag.trim();
      if (axisTag in settings) {
        return Math.max(axis.minValue, Math.min(axis.maxValue, settings[axisTag]));
      } else {
        return axis.defaultValue;
      }
    });
    var stream = new r.DecodeStream(this.stream.buffer);
    stream.pos = this._directoryPos;
    var font = new TTFFont(stream, coords);
    font._tables = this._tables;
    return font;
  };
  // Standardized format plugin API
  _proto43.getFont = function getFont(name) {
    return this.getVariation(name);
  };
  return _createClass(TTFFont, [{
    key: "postscriptName",
    get: function get() {
      return this.getName('postscriptName');
    }
    /**
     * The font's full name, e.g. "Helvetica Bold"
     * @type {string}
     */
  }, {
    key: "fullName",
    get: function get() {
      return this.getName('fullName');
    }
    /**
     * The font's family name, e.g. "Helvetica"
     * @type {string}
     */
  }, {
    key: "familyName",
    get: function get() {
      return this.getName('fontFamily');
    }
    /**
     * The font's sub-family, e.g. "Bold".
     * @type {string}
     */
  }, {
    key: "subfamilyName",
    get: function get() {
      return this.getName('fontSubfamily');
    }
    /**
     * The font's copyright information
     * @type {string}
     */
  }, {
    key: "copyright",
    get: function get() {
      return this.getName('copyright');
    }
    /**
     * The font's version number
     * @type {string}
     */
  }, {
    key: "version",
    get: function get() {
      return this.getName('version');
    }
    /**
     * The font’s [ascender](https://en.wikipedia.org/wiki/Ascender_(typography))
     * @type {number}
     */
  }, {
    key: "ascent",
    get: function get() {
      return this.hhea.ascent;
    }
    /**
     * The font’s [descender](https://en.wikipedia.org/wiki/Descender)
     * @type {number}
     */
  }, {
    key: "descent",
    get: function get() {
      return this.hhea.descent;
    }
    /**
     * The amount of space that should be included between lines
     * @type {number}
     */
  }, {
    key: "lineGap",
    get: function get() {
      return this.hhea.lineGap;
    }
    /**
     * The offset from the normal underline position that should be used
     * @type {number}
     */
  }, {
    key: "underlinePosition",
    get: function get() {
      return this.post.underlinePosition;
    }
    /**
     * The weight of the underline that should be used
     * @type {number}
     */
  }, {
    key: "underlineThickness",
    get: function get() {
      return this.post.underlineThickness;
    }
    /**
     * If this is an italic font, the angle the cursor should be drawn at to match the font design
     * @type {number}
     */
  }, {
    key: "italicAngle",
    get: function get() {
      return this.post.italicAngle;
    }
    /**
     * The height of capital letters above the baseline.
     * See [here](https://en.wikipedia.org/wiki/Cap_height) for more details.
     * @type {number}
     */
  }, {
    key: "capHeight",
    get: function get() {
      var os2 = this['OS/2'];
      return os2 ? os2.capHeight : this.ascent;
    }
    /**
     * The height of lower case letters in the font.
     * See [here](https://en.wikipedia.org/wiki/X-height) for more details.
     * @type {number}
     */
  }, {
    key: "xHeight",
    get: function get() {
      var os2 = this['OS/2'];
      return os2 ? os2.xHeight : 0;
    }
    /**
     * The number of glyphs in the font.
     * @type {number}
     */
  }, {
    key: "numGlyphs",
    get: function get() {
      return this.maxp.numGlyphs;
    }
    /**
     * The size of the font’s internal coordinate grid
     * @type {number}
     */
  }, {
    key: "unitsPerEm",
    get: function get() {
      return this.head.unitsPerEm;
    }
    /**
     * The font’s bounding box, i.e. the box that encloses all glyphs in the font.
     * @type {BBox}
     */
  }, {
    key: "bbox",
    get: function get() {
      return Object.freeze(new BBox(this.head.xMin, this.head.yMin, this.head.xMax, this.head.yMax));
    }
  }, {
    key: "_cmapProcessor",
    get: function get() {
      return new CmapProcessor(this.cmap);
    }
    /**
     * An array of all of the unicode code points supported by the font.
     * @type {number[]}
     */
  }, {
    key: "characterSet",
    get: function get() {
      return this._cmapProcessor.getCharacterSet();
    }
  }, {
    key: "_layoutEngine",
    get: function get() {
      return new LayoutEngine(this);
    }
  }, {
    key: "availableFeatures",
    get: function get() {
      return this._layoutEngine.getAvailableFeatures();
    }
  }, {
    key: "variationAxes",
    get: function get() {
      var res = {};
      if (!this.fvar) {
        return res;
      }
      for (var _iterator76 = _createForOfIteratorHelperLoose(this.fvar.axis), _step76; !(_step76 = _iterator76()).done;) {
        var axis = _step76.value;
        res[axis.axisTag.trim()] = {
          name: axis.name.en,
          min: axis.minValue,
          default: axis.defaultValue,
          max: axis.maxValue
        };
      }
      return res;
    }
    /**
     * Returns an object describing the named variation instances
     * that the font designer has specified. Keys are variation names
     * and values are the variation settings for this instance.
     *
     * @type {object}
     */
  }, {
    key: "namedVariations",
    get: function get() {
      var res = {};
      if (!this.fvar) {
        return res;
      }
      for (var _iterator77 = _createForOfIteratorHelperLoose(this.fvar.instance), _step77; !(_step77 = _iterator77()).done;) {
        var instance = _step77.value;
        var settings = {};
        for (var i = 0; i < this.fvar.axis.length; i++) {
          var axis = this.fvar.axis[i];
          settings[axis.axisTag.trim()] = instance.coord[i];
        }
        res[instance.name.en] = settings;
      }
      return res;
    }
  }, {
    key: "_variationProcessor",
    get: function get() {
      if (!this.fvar) {
        return null;
      }
      var variationCoords = this.variationCoords; // Ignore if no variation coords and not CFF2

      if (!variationCoords && !this.CFF2) {
        return null;
      }
      if (!variationCoords) {
        variationCoords = this.fvar.axis.map(function (axis) {
          return axis.defaultValue;
        });
      }
      return new GlyphVariationProcessor(this, variationCoords);
    }
  }]);
}(), _applyDecoratedDescriptor(_class$4.prototype, "bbox", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "bbox"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "_cmapProcessor", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "_cmapProcessor"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "characterSet", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "characterSet"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "_layoutEngine", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "_layoutEngine"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "variationAxes", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "variationAxes"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "namedVariations", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "namedVariations"), _class$4.prototype), _applyDecoratedDescriptor(_class$4.prototype, "_variationProcessor", [cache], Object.getOwnPropertyDescriptor(_class$4.prototype, "_variationProcessor"), _class$4.prototype), _class$4);
var WOFFDirectoryEntry = new r.Struct({
  tag: new r.String(4),
  offset: new r.Pointer(r.uint32, 'void', {
    type: 'global'
  }),
  compLength: r.uint32,
  length: r.uint32,
  origChecksum: r.uint32
});
var WOFFDirectory = new r.Struct({
  tag: new r.String(4),
  // should be 'wOFF'
  flavor: r.uint32,
  length: r.uint32,
  numTables: r.uint16,
  reserved: new r.Reserved(r.uint16),
  totalSfntSize: r.uint32,
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  metaOffset: r.uint32,
  metaLength: r.uint32,
  metaOrigLength: r.uint32,
  privOffset: r.uint32,
  privLength: r.uint32,
  tables: new r.Array(WOFFDirectoryEntry, 'numTables')
});
WOFFDirectory.process = function () {
  var tables = {};
  for (var _iterator78 = _createForOfIteratorHelperLoose(this.tables), _step78; !(_step78 = _iterator78()).done;) {
    var table = _step78.value;
    tables[table.tag] = table;
  }
  this.tables = tables;
};
var WOFFFont = /*#__PURE__*/function (_TTFFont) {
  function WOFFFont() {
    return _TTFFont.apply(this, arguments) || this;
  }
  _inheritsLoose(WOFFFont, _TTFFont);
  WOFFFont.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'wOFF';
  };
  var _proto44 = WOFFFont.prototype;
  _proto44._decodeDirectory = function _decodeDirectory() {
    this.directory = WOFFDirectory.decode(this.stream, {
      _startOffset: 0
    });
  };
  _proto44._getTableStream = function _getTableStream(tag) {
    var table = this.directory.tables[tag];
    if (table) {
      this.stream.pos = table.offset;
      if (table.compLength < table.length) {
        this.stream.pos += 2; // skip deflate header

        var outBuffer = Buffer.alloc(table.length);
        var buf = inflate(this.stream.readBuffer(table.compLength - 2), outBuffer);
        return new r.DecodeStream(buf);
      } else {
        return this.stream;
      }
    }
    return null;
  };
  return WOFFFont;
}(TTFFont);
/**
 * Represents a TrueType glyph in the WOFF2 format, which compresses glyphs differently.
 */
var WOFF2Glyph = /*#__PURE__*/function (_TTFGlyph2) {
  function WOFF2Glyph() {
    return _TTFGlyph2.apply(this, arguments) || this;
  }
  _inheritsLoose(WOFF2Glyph, _TTFGlyph2);
  var _proto45 = WOFF2Glyph.prototype;
  _proto45._decode = function _decode() {
    // We have to decode in advance (in WOFF2Font), so just return the pre-decoded data.
    return this._font._transformedGlyphs[this.id];
  };
  _proto45._getCBox = function _getCBox() {
    return this.path.bbox;
  };
  return WOFF2Glyph;
}(TTFGlyph);
var Base128 = {
  decode: function decode(stream) {
    var result = 0;
    var iterable = [0, 1, 2, 3, 4];
    for (var j = 0; j < iterable.length; j++) {
      var code = stream.readUInt8(); // If any of the top seven bits are set then we're about to overflow.

      if (result & 0xe0000000) {
        throw new Error('Overflow');
      }
      result = result << 7 | code & 0x7f;
      if ((code & 0x80) === 0) {
        return result;
      }
    }
    throw new Error('Bad base 128 number');
  }
};
var knownTags = ['cmap', 'head', 'hhea', 'hmtx', 'maxp', 'name', 'OS/2', 'post', 'cvt ', 'fpgm', 'glyf', 'loca', 'prep', 'CFF ', 'VORG', 'EBDT', 'EBLC', 'gasp', 'hdmx', 'kern', 'LTSH', 'PCLT', 'VDMX', 'vhea', 'vmtx', 'BASE', 'GDEF', 'GPOS', 'GSUB', 'EBSC', 'JSTF', 'MATH', 'CBDT', 'CBLC', 'COLR', 'CPAL', 'SVG ', 'sbix', 'acnt', 'avar', 'bdat', 'bloc', 'bsln', 'cvar', 'fdsc', 'feat', 'fmtx', 'fvar', 'gvar', 'hsty', 'just', 'lcar', 'mort', 'morx', 'opbd', 'prop', 'trak', 'Zapf', 'Silf', 'Glat', 'Gloc', 'Feat', 'Sill'];
var WOFF2DirectoryEntry = new r.Struct({
  flags: r.uint8,
  customTag: new r.Optional(new r.String(4), function (t) {
    return (t.flags & 0x3f) === 0x3f;
  }),
  tag: function tag(t) {
    return t.customTag || knownTags[t.flags & 0x3f];
  },
  // || (() => { throw new Error(`Bad tag: ${flags & 0x3f}`); })(); },
  length: Base128,
  transformVersion: function transformVersion(t) {
    return t.flags >>> 6 & 0x03;
  },
  transformed: function transformed(t) {
    return t.tag === 'glyf' || t.tag === 'loca' ? t.transformVersion === 0 : t.transformVersion !== 0;
  },
  transformLength: new r.Optional(Base128, function (t) {
    return t.transformed;
  })
});
var WOFF2Directory = new r.Struct({
  tag: new r.String(4),
  // should be 'wOF2'
  flavor: r.uint32,
  length: r.uint32,
  numTables: r.uint16,
  reserved: new r.Reserved(r.uint16),
  totalSfntSize: r.uint32,
  totalCompressedSize: r.uint32,
  majorVersion: r.uint16,
  minorVersion: r.uint16,
  metaOffset: r.uint32,
  metaLength: r.uint32,
  metaOrigLength: r.uint32,
  privOffset: r.uint32,
  privLength: r.uint32,
  tables: new r.Array(WOFF2DirectoryEntry, 'numTables')
});
WOFF2Directory.process = function () {
  var tables = {};
  for (var i = 0; i < this.tables.length; i++) {
    var table = this.tables[i];
    tables[table.tag] = table;
  }
  return this.tables = tables;
};

/**
 * Subclass of TTFFont that represents a TTF/OTF font compressed by WOFF2
 * See spec here: http://www.w3.org/TR/WOFF2/
 */
var WOFF2Font = /*#__PURE__*/function (_TTFFont2) {
  function WOFF2Font() {
    return _TTFFont2.apply(this, arguments) || this;
  }
  _inheritsLoose(WOFF2Font, _TTFFont2);
  WOFF2Font.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'wOF2';
  };
  var _proto46 = WOFF2Font.prototype;
  _proto46._decodeDirectory = function _decodeDirectory() {
    this.directory = WOFF2Directory.decode(this.stream);
    this._dataPos = this.stream.pos;
  };
  _proto46._decompress = function _decompress() {
    // decompress data and setup table offsets if we haven't already
    if (!this._decompressed) {
      this.stream.pos = this._dataPos;
      var buffer = this.stream.readBuffer(this.directory.totalCompressedSize);
      var decompressedSize = 0;
      for (var tag in this.directory.tables) {
        var entry = this.directory.tables[tag];
        entry.offset = decompressedSize;
        decompressedSize += entry.transformLength != null ? entry.transformLength : entry.length;
      }
      var decompressed = brotli(buffer, decompressedSize);
      if (!decompressed) {
        throw new Error('Error decoding compressed data in WOFF2');
      }
      this.stream = new r.DecodeStream(Buffer.from(decompressed));
      this._decompressed = true;
    }
  };
  _proto46._decodeTable = function _decodeTable(table) {
    this._decompress();
    return _TTFFont2.prototype._decodeTable.call(this, table);
  } // Override this method to get a glyph and return our
  // custom subclass if there is a glyf table.
  ;
  _proto46._getBaseGlyph = function _getBaseGlyph(glyph, characters) {
    if (characters === void 0) {
      characters = [];
    }
    if (!this._glyphs[glyph]) {
      if (this.directory.tables.glyf && this.directory.tables.glyf.transformed) {
        if (!this._transformedGlyphs) {
          this._transformGlyfTable();
        }
        return this._glyphs[glyph] = new WOFF2Glyph(glyph, characters, this);
      } else {
        return _TTFFont2.prototype._getBaseGlyph.call(this, glyph, characters);
      }
    }
  };
  _proto46._transformGlyfTable = function _transformGlyfTable() {
    this._decompress();
    this.stream.pos = this.directory.tables.glyf.offset;
    var table = GlyfTable.decode(this.stream);
    var glyphs = [];
    for (var index = 0; index < table.numGlyphs; index++) {
      var glyph = {};
      var nContours = table.nContours.readInt16BE();
      glyph.numberOfContours = nContours;
      if (nContours > 0) {
        // simple glyph
        var nPoints = [];
        var totalPoints = 0;
        for (var i = 0; i < nContours; i++) {
          var _r = read255UInt16(table.nPoints);
          totalPoints += _r;
          nPoints.push(totalPoints);
        }
        glyph.points = decodeTriplet(table.flags, table.glyphs, totalPoints);
        for (var _i = 0; _i < nContours; _i++) {
          glyph.points[nPoints[_i] - 1].endContour = true;
        }
        var instructionSize = read255UInt16(table.glyphs);
      } else if (nContours < 0) {
        // composite glyph
        var haveInstructions = TTFGlyph.prototype._decodeComposite.call({
          _font: this
        }, glyph, table.composites);
        if (haveInstructions) {
          var instructionSize = read255UInt16(table.glyphs);
        }
      }
      glyphs.push(glyph);
    }
    this._transformedGlyphs = glyphs;
  };
  return WOFF2Font;
}(TTFFont); // Special class that accepts a length and returns a sub-stream for that data
var Substream = /*#__PURE__*/function () {
  function Substream(length) {
    this.length = length;
    this._buf = new r.Buffer(length);
  }
  var _proto47 = Substream.prototype;
  _proto47.decode = function decode(stream, parent) {
    return new r.DecodeStream(this._buf.decode(stream, parent));
  };
  return Substream;
}(); // This struct represents the entire glyf table
var GlyfTable = new r.Struct({
  version: r.uint32,
  numGlyphs: r.uint16,
  indexFormat: r.uint16,
  nContourStreamSize: r.uint32,
  nPointsStreamSize: r.uint32,
  flagStreamSize: r.uint32,
  glyphStreamSize: r.uint32,
  compositeStreamSize: r.uint32,
  bboxStreamSize: r.uint32,
  instructionStreamSize: r.uint32,
  nContours: new Substream('nContourStreamSize'),
  nPoints: new Substream('nPointsStreamSize'),
  flags: new Substream('flagStreamSize'),
  glyphs: new Substream('glyphStreamSize'),
  composites: new Substream('compositeStreamSize'),
  bboxes: new Substream('bboxStreamSize'),
  instructions: new Substream('instructionStreamSize')
});
var WORD_CODE = 253;
var ONE_MORE_BYTE_CODE2 = 254;
var ONE_MORE_BYTE_CODE1 = 255;
var LOWEST_U_CODE = 253;
function read255UInt16(stream) {
  var code = stream.readUInt8();
  if (code === WORD_CODE) {
    return stream.readUInt16BE();
  }
  if (code === ONE_MORE_BYTE_CODE1) {
    return stream.readUInt8() + LOWEST_U_CODE;
  }
  if (code === ONE_MORE_BYTE_CODE2) {
    return stream.readUInt8() + LOWEST_U_CODE * 2;
  }
  return code;
}
function withSign(flag, baseval) {
  return flag & 1 ? baseval : -baseval;
}
function decodeTriplet(flags, glyphs, nPoints) {
  var y;
  var x = y = 0;
  var res = [];
  for (var i = 0; i < nPoints; i++) {
    var dx = 0,
      dy = 0;
    var flag = flags.readUInt8();
    var onCurve = !(flag >> 7);
    flag &= 0x7f;
    if (flag < 10) {
      dx = 0;
      dy = withSign(flag, ((flag & 14) << 7) + glyphs.readUInt8());
    } else if (flag < 20) {
      dx = withSign(flag, ((flag - 10 & 14) << 7) + glyphs.readUInt8());
      dy = 0;
    } else if (flag < 84) {
      var b0 = flag - 20;
      var b1 = glyphs.readUInt8();
      dx = withSign(flag, 1 + (b0 & 0x30) + (b1 >> 4));
      dy = withSign(flag >> 1, 1 + ((b0 & 0x0c) << 2) + (b1 & 0x0f));
    } else if (flag < 120) {
      var b0 = flag - 84;
      dx = withSign(flag, 1 + (b0 / 12 << 8) + glyphs.readUInt8());
      dy = withSign(flag >> 1, 1 + (b0 % 12 >> 2 << 8) + glyphs.readUInt8());
    } else if (flag < 124) {
      var b1 = glyphs.readUInt8();
      var b2 = glyphs.readUInt8();
      dx = withSign(flag, (b1 << 4) + (b2 >> 4));
      dy = withSign(flag >> 1, ((b2 & 0x0f) << 8) + glyphs.readUInt8());
    } else {
      dx = withSign(flag, glyphs.readUInt16BE());
      dy = withSign(flag >> 1, glyphs.readUInt16BE());
    }
    x += dx;
    y += dy;
    res.push(new Point(onCurve, false, x, y));
  }
  return res;
}
var TTCHeader = new r.VersionedStruct(r.uint32, {
  0x00010000: {
    numFonts: r.uint32,
    offsets: new r.Array(r.uint32, 'numFonts')
  },
  0x00020000: {
    numFonts: r.uint32,
    offsets: new r.Array(r.uint32, 'numFonts'),
    dsigTag: r.uint32,
    dsigLength: r.uint32,
    dsigOffset: r.uint32
  }
});
var TrueTypeCollection = /*#__PURE__*/function () {
  function TrueTypeCollection(stream) {
    this.stream = stream;
    if (stream.readString(4) !== 'ttcf') {
      throw new Error('Not a TrueType collection');
    }
    this.header = TTCHeader.decode(stream);
  }
  TrueTypeCollection.probe = function probe(buffer) {
    return buffer.toString('ascii', 0, 4) === 'ttcf';
  };
  var _proto48 = TrueTypeCollection.prototype;
  _proto48.getFont = function getFont(name) {
    for (var _iterator79 = _createForOfIteratorHelperLoose(this.header.offsets), _step79; !(_step79 = _iterator79()).done;) {
      var offset = _step79.value;
      var stream = new r.DecodeStream(this.stream.buffer);
      stream.pos = offset;
      var font = new TTFFont(stream);
      if (font.postscriptName === name) {
        return font;
      }
    }
    return null;
  };
  return _createClass(TrueTypeCollection, [{
    key: "fonts",
    get: function get() {
      var fonts = [];
      for (var _iterator80 = _createForOfIteratorHelperLoose(this.header.offsets), _step80; !(_step80 = _iterator80()).done;) {
        var offset = _step80.value;
        var stream = new r.DecodeStream(this.stream.buffer);
        stream.pos = offset;
        fonts.push(new TTFFont(stream));
      }
      return fonts;
    }
  }]);
}();
var DFontName = new r.String(r.uint8);
var DFontData = new r.Struct({
  len: r.uint32,
  buf: new r.Buffer('len')
});
var Ref = new r.Struct({
  id: r.uint16,
  nameOffset: r.int16,
  attr: r.uint8,
  dataOffset: r.uint24,
  handle: r.uint32
});
var Type = new r.Struct({
  name: new r.String(4),
  maxTypeIndex: r.uint16,
  refList: new r.Pointer(r.uint16, new r.Array(Ref, function (t) {
    return t.maxTypeIndex + 1;
  }), {
    type: 'parent'
  })
});
var TypeList = new r.Struct({
  length: r.uint16,
  types: new r.Array(Type, function (t) {
    return t.length + 1;
  })
});
var DFontMap = new r.Struct({
  reserved: new r.Reserved(r.uint8, 24),
  typeList: new r.Pointer(r.uint16, TypeList),
  nameListOffset: new r.Pointer(r.uint16, 'void')
});
var DFontHeader = new r.Struct({
  dataOffset: r.uint32,
  map: new r.Pointer(r.uint32, DFontMap),
  dataLength: r.uint32,
  mapLength: r.uint32
});
var DFont = /*#__PURE__*/function () {
  function DFont(stream) {
    this.stream = stream;
    this.header = DFontHeader.decode(this.stream);
    for (var _iterator81 = _createForOfIteratorHelperLoose(this.header.map.typeList.types), _step81; !(_step81 = _iterator81()).done;) {
      var type = _step81.value;
      for (var _iterator82 = _createForOfIteratorHelperLoose(type.refList), _step82; !(_step82 = _iterator82()).done;) {
        var ref = _step82.value;
        if (ref.nameOffset >= 0) {
          this.stream.pos = ref.nameOffset + this.header.map.nameListOffset;
          ref.name = DFontName.decode(this.stream);
        } else {
          ref.name = null;
        }
      }
      if (type.name === 'sfnt') {
        this.sfnt = type;
      }
    }
  }
  DFont.probe = function probe(buffer) {
    var stream = new r.DecodeStream(buffer);
    try {
      var header = DFontHeader.decode(stream);
    } catch (e) {
      return false;
    }
    for (var _iterator83 = _createForOfIteratorHelperLoose(header.map.typeList.types), _step83; !(_step83 = _iterator83()).done;) {
      var type = _step83.value;
      if (type.name === 'sfnt') {
        return true;
      }
    }
    return false;
  };
  var _proto49 = DFont.prototype;
  _proto49.getFont = function getFont(name) {
    if (!this.sfnt) {
      return null;
    }
    for (var _iterator84 = _createForOfIteratorHelperLoose(this.sfnt.refList), _step84; !(_step84 = _iterator84()).done;) {
      var ref = _step84.value;
      var pos = this.header.dataOffset + ref.dataOffset + 4;
      var stream = new r.DecodeStream(this.stream.buffer.slice(pos));
      var font = new TTFFont(stream);
      if (font.postscriptName === name) {
        return font;
      }
    }
    return null;
  };
  return _createClass(DFont, [{
    key: "fonts",
    get: function get() {
      var fonts = [];
      for (var _iterator85 = _createForOfIteratorHelperLoose(this.sfnt.refList), _step85; !(_step85 = _iterator85()).done;) {
        var ref = _step85.value;
        var pos = this.header.dataOffset + ref.dataOffset + 4;
        var stream = new r.DecodeStream(this.stream.buffer.slice(pos));
        fonts.push(new TTFFont(stream));
      }
      return fonts;
    }
  }]);
}();
fontkit.registerFormat(TTFFont);
fontkit.registerFormat(WOFFFont);
fontkit.registerFormat(WOFF2Font);
fontkit.registerFormat(TrueTypeCollection);
fontkit.registerFormat(DFont);
module.exports = fontkit;

/***/ }),

/***/ 5417:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var UnicodeTrie = __webpack_require__(59006);

var base64 = __webpack_require__(32504);
var _require = __webpack_require__(84490),
  BK = _require.BK,
  CR = _require.CR,
  LF = _require.LF,
  NL = _require.NL,
  SG = _require.SG,
  WJ = _require.WJ,
  SP = _require.SP,
  ZWJ = _require.ZWJ,
  BA = _require.BA,
  HY = _require.HY,
  NS = _require.NS,
  AI = _require.AI,
  AL = _require.AL,
  CJ = _require.CJ,
  HL = _require.HL,
  RI = _require.RI,
  SA = _require.SA,
  XX = _require.XX;
var _require2 = __webpack_require__(5303),
  DI_BRK = _require2.DI_BRK,
  IN_BRK = _require2.IN_BRK,
  CI_BRK = _require2.CI_BRK,
  CP_BRK = _require2.CP_BRK,
  PR_BRK = _require2.PR_BRK,
  pairTable = _require2.pairTable;
var data = base64.toByteArray("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");
var classTrie = new UnicodeTrie(data);
var mapClass = function mapClass(c) {
  switch (c) {
    case AI:
      return AL;
    case SA:
    case SG:
    case XX:
      return AL;
    case CJ:
      return NS;
    default:
      return c;
  }
};
var mapFirst = function mapFirst(c) {
  switch (c) {
    case LF:
    case NL:
      return BK;
    case SP:
      return WJ;
    default:
      return c;
  }
};
var Break = function Break(position, required) {
  if (required === void 0) {
    required = false;
  }
  this.position = position;
  this.required = required;
};
var LineBreaker = /*#__PURE__*/function () {
  function LineBreaker(string) {
    this.string = string;
    this.pos = 0;
    this.lastPos = 0;
    this.curClass = null;
    this.nextClass = null;
    this.LB8a = false;
    this.LB21a = false;
    this.LB30a = 0;
  }
  var _proto = LineBreaker.prototype;
  _proto.nextCodePoint = function nextCodePoint() {
    var code = this.string.charCodeAt(this.pos++);
    var next = this.string.charCodeAt(this.pos);

    // If a surrogate pair
    if (0xd800 <= code && code <= 0xdbff && 0xdc00 <= next && next <= 0xdfff) {
      this.pos++;
      return (code - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000;
    }
    return code;
  };
  _proto.nextCharClass = function nextCharClass() {
    return mapClass(classTrie.get(this.nextCodePoint()));
  };
  _proto.getSimpleBreak = function getSimpleBreak() {
    // handle classes not handled by the pair table
    switch (this.nextClass) {
      case SP:
        return false;
      case BK:
      case LF:
      case NL:
        this.curClass = BK;
        return false;
      case CR:
        this.curClass = CR;
        return false;
    }
    return null;
  };
  _proto.getPairTableBreak = function getPairTableBreak(lastClass) {
    // if not handled already, use the pair table
    var shouldBreak = false;
    switch (pairTable[this.curClass][this.nextClass]) {
      case DI_BRK:
        // Direct break
        shouldBreak = true;
        break;
      case IN_BRK:
        // possible indirect break
        shouldBreak = lastClass === SP;
        break;
      case CI_BRK:
        shouldBreak = lastClass === SP;
        if (!shouldBreak) {
          shouldBreak = false;
          return shouldBreak;
        }
        break;
      case CP_BRK:
        // prohibited for combining marks
        if (lastClass !== SP) {
          return shouldBreak;
        }
        break;
      case PR_BRK:
        break;
    }
    if (this.LB8a) {
      shouldBreak = false;
    }

    // Rule LB21a
    if (this.LB21a && (this.curClass === HY || this.curClass === BA)) {
      shouldBreak = false;
      this.LB21a = false;
    } else {
      this.LB21a = this.curClass === HL;
    }

    // Rule LB30a
    if (this.curClass === RI) {
      this.LB30a++;
      if (this.LB30a == 2 && this.nextClass === RI) {
        shouldBreak = true;
        this.LB30a = 0;
      }
    } else {
      this.LB30a = 0;
    }
    this.curClass = this.nextClass;
    return shouldBreak;
  };
  _proto.nextBreak = function nextBreak() {
    // get the first char if we're at the beginning of the string
    if (this.curClass == null) {
      var firstClass = this.nextCharClass();
      this.curClass = mapFirst(firstClass);
      this.nextClass = firstClass;
      this.LB8a = firstClass === ZWJ;
      this.LB30a = 0;
    }
    while (this.pos < this.string.length) {
      this.lastPos = this.pos;
      var lastClass = this.nextClass;
      this.nextClass = this.nextCharClass();

      // explicit newline
      if (this.curClass === BK || this.curClass === CR && this.nextClass !== LF) {
        this.curClass = mapFirst(mapClass(this.nextClass));
        return new Break(this.lastPos, true);
      }
      var shouldBreak = this.getSimpleBreak();
      if (shouldBreak === null) {
        shouldBreak = this.getPairTableBreak(lastClass);
      }

      // Rule LB8a
      this.LB8a = this.nextClass === ZWJ;
      if (shouldBreak) {
        return new Break(this.lastPos);
      }
    }
    if (this.lastPos < this.string.length) {
      this.lastPos = this.string.length;
      return new Break(this.string.length);
    }
    return null;
  };
  return LineBreaker;
}();
module.exports = LineBreaker;

/***/ }),

/***/ 50477:
/***/ (function() {

/* (ignored) */

/***/ }),

/***/ 16403:
/***/ (function() {

/* (ignored) */

/***/ }),

/***/ 63779:
/***/ (function() {

/* (ignored) */

/***/ }),

/***/ 77199:
/***/ (function() {

/* (ignored) */

/***/ }),

/***/ 10821:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

"use strict";


var possibleNames = __webpack_require__(10884);

var g = typeof globalThis === 'undefined' ? __webpack_require__.g : globalThis;

/** @type {import('.')} */
module.exports = function availableTypedArrays() {
	var /** @type {ReturnType<typeof availableTypedArrays>} */ out = [];
	for (var i = 0; i < possibleNames.length; i++) {
		if (typeof g[possibleNames[i]] === 'function') {
			// @ts-expect-error
			out[out.length] = possibleNames[i];
		}
	}
	return out;
};


/***/ }),

/***/ 24766:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var $c5L0i$base64js = __webpack_require__(32504);
var $c5L0i$unicodetrie = __webpack_require__(59006);

function $parcel$interopDefault(a) {
  return a && a.__esModule ? a.default : a;
}
function $parcel$defineInteropFlag(a) {
  Object.defineProperty(a, '__esModule', {value: true, configurable: true});
}
function $parcel$export(e, n, v, s) {
  Object.defineProperty(e, n, {get: v, set: s, enumerable: true, configurable: true});
}

$parcel$defineInteropFlag(module.exports);

$parcel$export(module.exports, "getCategory", () => $43d7963e56408b24$export$410364bbb673ddbc);
$parcel$export(module.exports, "getCombiningClass", () => $43d7963e56408b24$export$c03b919c6651ed55);
$parcel$export(module.exports, "getScript", () => $43d7963e56408b24$export$941569448d136665);
$parcel$export(module.exports, "getEastAsianWidth", () => $43d7963e56408b24$export$92f6187db8ca6d26);
$parcel$export(module.exports, "getNumericValue", () => $43d7963e56408b24$export$7d1258ebb7625a0d);
$parcel$export(module.exports, "isAlphabetic", () => $43d7963e56408b24$export$52c8ea63abd07594);
$parcel$export(module.exports, "isDigit", () => $43d7963e56408b24$export$727d9dbc4fbb948f);
$parcel$export(module.exports, "isPunctuation", () => $43d7963e56408b24$export$a5b49f4dc6a07d2c);
$parcel$export(module.exports, "isLowerCase", () => $43d7963e56408b24$export$7b6804e8df61fcf5);
$parcel$export(module.exports, "isUpperCase", () => $43d7963e56408b24$export$aebd617640818cda);
$parcel$export(module.exports, "isTitleCase", () => $43d7963e56408b24$export$de8b4ee23b2cf823);
$parcel$export(module.exports, "isWhiteSpace", () => $43d7963e56408b24$export$3c52dd84024ae72c);
$parcel$export(module.exports, "isBaseForm", () => $43d7963e56408b24$export$a11bdcffe109e74b);
$parcel$export(module.exports, "isMark", () => $43d7963e56408b24$export$e33ad6871e762338);
$parcel$export(module.exports, "default", () => $43d7963e56408b24$export$2e2bcd8739ae039);


var $29668e65f2091c2c$exports = {};
$29668e65f2091c2c$exports = JSON.parse('{"categories":["Cc","Zs","Po","Sc","Ps","Pe","Sm","Pd","Nd","Lu","Sk","Pc","Ll","So","Lo","Pi","Cf","No","Pf","Lt","Lm","Mn","Me","Mc","Nl","Zl","Zp","Cs","Co"],"combiningClasses":["Not_Reordered","Above","Above_Right","Below","Attached_Above_Right","Attached_Below","Overlay","Iota_Subscript","Double_Below","Double_Above","Below_Right","Above_Left","CCC10","CCC11","CCC12","CCC13","CCC14","CCC15","CCC16","CCC17","CCC18","CCC19","CCC20","CCC21","CCC22","CCC23","CCC24","CCC25","CCC30","CCC31","CCC32","CCC27","CCC28","CCC29","CCC33","CCC34","CCC35","CCC36","Nukta","Virama","CCC84","CCC91","CCC103","CCC107","CCC118","CCC122","CCC129","CCC130","CCC132","Attached_Above","Below_Left","Left","Kana_Voicing","CCC26","Right"],"scripts":["Common","Latin","Bopomofo","Inherited","Greek","Coptic","Cyrillic","Armenian","Hebrew","Arabic","Syriac","Thaana","Nko","Samaritan","Mandaic","Devanagari","Bengali","Gurmukhi","Gujarati","Oriya","Tamil","Telugu","Kannada","Malayalam","Sinhala","Thai","Lao","Tibetan","Myanmar","Georgian","Hangul","Ethiopic","Cherokee","Canadian_Aboriginal","Ogham","Runic","Tagalog","Hanunoo","Buhid","Tagbanwa","Khmer","Mongolian","Limbu","Tai_Le","New_Tai_Lue","Buginese","Tai_Tham","Balinese","Sundanese","Batak","Lepcha","Ol_Chiki","Braille","Glagolitic","Tifinagh","Han","Hiragana","Katakana","Yi","Lisu","Vai","Bamum","Syloti_Nagri","Phags_Pa","Saurashtra","Kayah_Li","Rejang","Javanese","Cham","Tai_Viet","Meetei_Mayek","null","Linear_B","Lycian","Carian","Old_Italic","Gothic","Old_Permic","Ugaritic","Old_Persian","Deseret","Shavian","Osmanya","Osage","Elbasan","Caucasian_Albanian","Linear_A","Cypriot","Imperial_Aramaic","Palmyrene","Nabataean","Hatran","Phoenician","Lydian","Meroitic_Hieroglyphs","Meroitic_Cursive","Kharoshthi","Old_South_Arabian","Old_North_Arabian","Manichaean","Avestan","Inscriptional_Parthian","Inscriptional_Pahlavi","Psalter_Pahlavi","Old_Turkic","Old_Hungarian","Hanifi_Rohingya","Old_Sogdian","Sogdian","Elymaic","Brahmi","Kaithi","Sora_Sompeng","Chakma","Mahajani","Sharada","Khojki","Multani","Khudawadi","Grantha","Newa","Tirhuta","Siddham","Modi","Takri","Ahom","Dogra","Warang_Citi","Nandinagari","Zanabazar_Square","Soyombo","Pau_Cin_Hau","Bhaiksuki","Marchen","Masaram_Gondi","Gunjala_Gondi","Makasar","Cuneiform","Egyptian_Hieroglyphs","Anatolian_Hieroglyphs","Mro","Bassa_Vah","Pahawh_Hmong","Medefaidrin","Miao","Tangut","Nushu","Duployan","SignWriting","Nyiakeng_Puachue_Hmong","Wancho","Mende_Kikakui","Adlam"],"eaw":["N","Na","A","W","H","F"]}');


const $43d7963e56408b24$var$trie = new (0, ($parcel$interopDefault($c5L0i$unicodetrie)))((0, ($parcel$interopDefault($c5L0i$base64js))).toByteArray("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"));
const $43d7963e56408b24$var$log2 = Math.log2 || ((n)=>Math.log(n) / Math.LN2);
const $43d7963e56408b24$var$bits = (n)=>$43d7963e56408b24$var$log2(n) + 1 | 0;
// compute the number of bits stored for each field
const $43d7963e56408b24$var$CATEGORY_BITS = $43d7963e56408b24$var$bits((0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).categories.length - 1);
const $43d7963e56408b24$var$COMBINING_BITS = $43d7963e56408b24$var$bits((0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).combiningClasses.length - 1);
const $43d7963e56408b24$var$SCRIPT_BITS = $43d7963e56408b24$var$bits((0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).scripts.length - 1);
const $43d7963e56408b24$var$EAW_BITS = $43d7963e56408b24$var$bits((0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).eaw.length - 1);
const $43d7963e56408b24$var$NUMBER_BITS = 10;
// compute shift and mask values for each field
const $43d7963e56408b24$var$CATEGORY_SHIFT = $43d7963e56408b24$var$COMBINING_BITS + $43d7963e56408b24$var$SCRIPT_BITS + $43d7963e56408b24$var$EAW_BITS + $43d7963e56408b24$var$NUMBER_BITS;
const $43d7963e56408b24$var$COMBINING_SHIFT = $43d7963e56408b24$var$SCRIPT_BITS + $43d7963e56408b24$var$EAW_BITS + $43d7963e56408b24$var$NUMBER_BITS;
const $43d7963e56408b24$var$SCRIPT_SHIFT = $43d7963e56408b24$var$EAW_BITS + $43d7963e56408b24$var$NUMBER_BITS;
const $43d7963e56408b24$var$EAW_SHIFT = $43d7963e56408b24$var$NUMBER_BITS;
const $43d7963e56408b24$var$CATEGORY_MASK = (1 << $43d7963e56408b24$var$CATEGORY_BITS) - 1;
const $43d7963e56408b24$var$COMBINING_MASK = (1 << $43d7963e56408b24$var$COMBINING_BITS) - 1;
const $43d7963e56408b24$var$SCRIPT_MASK = (1 << $43d7963e56408b24$var$SCRIPT_BITS) - 1;
const $43d7963e56408b24$var$EAW_MASK = (1 << $43d7963e56408b24$var$EAW_BITS) - 1;
const $43d7963e56408b24$var$NUMBER_MASK = (1 << $43d7963e56408b24$var$NUMBER_BITS) - 1;
function $43d7963e56408b24$export$410364bbb673ddbc(codePoint) {
    const val = $43d7963e56408b24$var$trie.get(codePoint);
    return (0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).categories[val >> $43d7963e56408b24$var$CATEGORY_SHIFT & $43d7963e56408b24$var$CATEGORY_MASK];
}
function $43d7963e56408b24$export$c03b919c6651ed55(codePoint) {
    const val = $43d7963e56408b24$var$trie.get(codePoint);
    return (0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).combiningClasses[val >> $43d7963e56408b24$var$COMBINING_SHIFT & $43d7963e56408b24$var$COMBINING_MASK];
}
function $43d7963e56408b24$export$941569448d136665(codePoint) {
    const val = $43d7963e56408b24$var$trie.get(codePoint);
    return (0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).scripts[val >> $43d7963e56408b24$var$SCRIPT_SHIFT & $43d7963e56408b24$var$SCRIPT_MASK];
}
function $43d7963e56408b24$export$92f6187db8ca6d26(codePoint) {
    const val = $43d7963e56408b24$var$trie.get(codePoint);
    return (0, (/*@__PURE__*/$parcel$interopDefault($29668e65f2091c2c$exports))).eaw[val >> $43d7963e56408b24$var$EAW_SHIFT & $43d7963e56408b24$var$EAW_MASK];
}
function $43d7963e56408b24$export$7d1258ebb7625a0d(codePoint) {
    let val = $43d7963e56408b24$var$trie.get(codePoint);
    let num = val & $43d7963e56408b24$var$NUMBER_MASK;
    if (num === 0) return null;
    else if (num <= 50) return num - 1;
    else if (num < 0x1e0) {
        const numerator = (num >> 4) - 12;
        const denominator = (num & 0xf) + 1;
        return numerator / denominator;
    } else if (num < 0x300) {
        val = (num >> 5) - 14;
        let exp = (num & 0x1f) + 2;
        while(exp > 0){
            val *= 10;
            exp--;
        }
        return val;
    } else {
        val = (num >> 2) - 0xbf;
        let exp = (num & 3) + 1;
        while(exp > 0){
            val *= 60;
            exp--;
        }
        return val;
    }
}
function $43d7963e56408b24$export$52c8ea63abd07594(codePoint) {
    const category = $43d7963e56408b24$export$410364bbb673ddbc(codePoint);
    return category === "Lu" || category === "Ll" || category === "Lt" || category === "Lm" || category === "Lo" || category === "Nl";
}
function $43d7963e56408b24$export$727d9dbc4fbb948f(codePoint) {
    return $43d7963e56408b24$export$410364bbb673ddbc(codePoint) === "Nd";
}
function $43d7963e56408b24$export$a5b49f4dc6a07d2c(codePoint) {
    const category = $43d7963e56408b24$export$410364bbb673ddbc(codePoint);
    return category === "Pc" || category === "Pd" || category === "Pe" || category === "Pf" || category === "Pi" || category === "Po" || category === "Ps";
}
function $43d7963e56408b24$export$7b6804e8df61fcf5(codePoint) {
    return $43d7963e56408b24$export$410364bbb673ddbc(codePoint) === "Ll";
}
function $43d7963e56408b24$export$aebd617640818cda(codePoint) {
    return $43d7963e56408b24$export$410364bbb673ddbc(codePoint) === "Lu";
}
function $43d7963e56408b24$export$de8b4ee23b2cf823(codePoint) {
    return $43d7963e56408b24$export$410364bbb673ddbc(codePoint) === "Lt";
}
function $43d7963e56408b24$export$3c52dd84024ae72c(codePoint) {
    const category = $43d7963e56408b24$export$410364bbb673ddbc(codePoint);
    return category === "Zs" || category === "Zl" || category === "Zp";
}
function $43d7963e56408b24$export$a11bdcffe109e74b(codePoint) {
    const category = $43d7963e56408b24$export$410364bbb673ddbc(codePoint);
    return category === "Nd" || category === "No" || category === "Nl" || category === "Lu" || category === "Ll" || category === "Lt" || category === "Lm" || category === "Lo" || category === "Me" || category === "Mc";
}
function $43d7963e56408b24$export$e33ad6871e762338(codePoint) {
    const category = $43d7963e56408b24$export$410364bbb673ddbc(codePoint);
    return category === "Mn" || category === "Me" || category === "Mc";
}
var // Backwards compatibility.
$43d7963e56408b24$export$2e2bcd8739ae039 = {
    getCategory: $43d7963e56408b24$export$410364bbb673ddbc,
    getCombiningClass: $43d7963e56408b24$export$c03b919c6651ed55,
    getScript: $43d7963e56408b24$export$941569448d136665,
    getEastAsianWidth: $43d7963e56408b24$export$92f6187db8ca6d26,
    getNumericValue: $43d7963e56408b24$export$7d1258ebb7625a0d,
    isAlphabetic: $43d7963e56408b24$export$52c8ea63abd07594,
    isDigit: $43d7963e56408b24$export$727d9dbc4fbb948f,
    isPunctuation: $43d7963e56408b24$export$a5b49f4dc6a07d2c,
    isLowerCase: $43d7963e56408b24$export$7b6804e8df61fcf5,
    isUpperCase: $43d7963e56408b24$export$aebd617640818cda,
    isTitleCase: $43d7963e56408b24$export$de8b4ee23b2cf823,
    isWhiteSpace: $43d7963e56408b24$export$3c52dd84024ae72c,
    isBaseForm: $43d7963e56408b24$export$a11bdcffe109e74b,
    isMark: $43d7963e56408b24$export$e33ad6871e762338
};




/***/ }),

/***/ 43267:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["8740","䏰䰲䘃䖦䕸𧉧䵷䖳𧲱䳢𧳅㮕䜶䝄䱇䱀𤊿𣘗𧍒𦺋𧃒䱗𪍑䝏䗚䲅𧱬䴇䪤䚡𦬣爥𥩔𡩣𣸆𣽡晍囻"],["8767","綕夝𨮹㷴霴𧯯寛𡵞媤㘥𩺰嫑宷峼杮薓𩥅瑡璝㡵𡵓𣚞𦀡㻬"],["87a1","𥣞㫵竼龗𤅡𨤍𣇪𠪊𣉞䌊蒄龖鐯䤰蘓墖靊鈘秐稲晠権袝瑌篅枂稬剏遆㓦珄𥶹瓆鿇垳䤯呌䄱𣚎堘穲𧭥讏䚮𦺈䆁𥶙箮𢒼鿈𢓁𢓉𢓌鿉蔄𣖻䂴鿊䓡𪷿拁灮鿋"],["8840","㇀",4,"𠄌㇅𠃑𠃍㇆㇇𠃋𡿨㇈𠃊㇉㇊㇋㇌𠄎㇍㇎ĀÁǍÀĒÉĚÈŌÓǑÒ࿿Ê̄Ế࿿Ê̌ỀÊāáǎàɑēéěèīíǐìōóǒòūúǔùǖǘǚ"],["88a1","ǜü࿿ê̄ế࿿ê̌ềêɡ⏚⏛"],["8940","𪎩𡅅"],["8943","攊"],["8946","丽滝鵎釟"],["894c","𧜵撑会伨侨兖兴农凤务动医华发变团声处备夲头学实実岚庆总斉柾栄桥济炼电纤纬纺织经统缆缷艺苏药视设询车轧轮"],["89a1","琑糼緍楆竉刧"],["89ab","醌碸酞肼"],["89b0","贋胶𠧧"],["89b5","肟黇䳍鷉鸌䰾𩷶𧀎鸊𪄳㗁"],["89c1","溚舾甙"],["89c5","䤑马骏龙禇𨑬𡷊𠗐𢫦两亁亀亇亿仫伷㑌侽㹈倃傈㑽㒓㒥円夅凛凼刅争剹劐匧㗇厩㕑厰㕓参吣㕭㕲㚁咓咣咴咹哐哯唘唣唨㖘唿㖥㖿嗗㗅"],["8a40","𧶄唥"],["8a43","𠱂𠴕𥄫喐𢳆㧬𠍁蹆𤶸𩓥䁓𨂾睺𢰸㨴䟕𨅝𦧲𤷪擝𠵼𠾴𠳕𡃴撍蹾𠺖𠰋𠽤𢲩𨉖𤓓"],["8a64","𠵆𩩍𨃩䟴𤺧𢳂骲㩧𩗴㿭㔆𥋇𩟔𧣈𢵄鵮頕"],["8a76","䏙𦂥撴哣𢵌𢯊𡁷㧻𡁯"],["8aa1","𦛚𦜖𧦠擪𥁒𠱃蹨𢆡𨭌𠜱"],["8aac","䠋𠆩㿺塳𢶍"],["8ab2","𤗈𠓼𦂗𠽌𠶖啹䂻䎺"],["8abb","䪴𢩦𡂝膪飵𠶜捹㧾𢝵跀嚡摼㹃"],["8ac9","𪘁𠸉𢫏𢳉"],["8ace","𡃈𣧂㦒㨆𨊛㕸𥹉𢃇噒𠼱𢲲𩜠㒼氽𤸻"],["8adf","𧕴𢺋𢈈𪙛𨳍𠹺𠰴𦠜羓𡃏𢠃𢤹㗻𥇣𠺌𠾍𠺪㾓𠼰𠵇𡅏𠹌"],["8af6","𠺫𠮩𠵈𡃀𡄽㿹𢚖搲𠾭"],["8b40","𣏴𧘹𢯎𠵾𠵿𢱑𢱕㨘𠺘𡃇𠼮𪘲𦭐𨳒𨶙𨳊閪哌苄喹"],["8b55","𩻃鰦骶𧝞𢷮煀腭胬尜𦕲脴㞗卟𨂽醶𠻺𠸏𠹷𠻻㗝𤷫㘉𠳖嚯𢞵𡃉𠸐𠹸𡁸𡅈𨈇𡑕𠹹𤹐𢶤婔𡀝𡀞𡃵𡃶垜𠸑"],["8ba1","𧚔𨋍𠾵𠹻𥅾㜃𠾶𡆀𥋘𪊽𤧚𡠺𤅷𨉼墙剨㘚𥜽箲孨䠀䬬鼧䧧鰟鮍𥭴𣄽嗻㗲嚉丨夂𡯁屮靑𠂆乛亻㔾尣彑忄㣺扌攵歺氵氺灬爫丬犭𤣩罒礻糹罓𦉪㓁"],["8bde","𦍋耂肀𦘒𦥑卝衤见𧢲讠贝钅镸长门𨸏韦页风飞饣𩠐鱼鸟黄歯龜丷𠂇阝户钢"],["8c40","倻淾𩱳龦㷉袏𤅎灷峵䬠𥇍㕙𥴰愢𨨲辧釶熑朙玺𣊁𪄇㲋𡦀䬐磤琂冮𨜏䀉橣𪊺䈣蘏𠩯稪𩥇𨫪靕灍匤𢁾鏴盙𨧣龧矝亣俰傼丯众龨吴綋墒壐𡶶庒庙忂𢜒斋"],["8ca1","𣏹椙橃𣱣泿"],["8ca7","爀𤔅玌㻛𤨓嬕璹讃𥲤𥚕窓篬糃繬苸薗龩袐龪躹龫迏蕟駠鈡龬𨶹𡐿䁱䊢娚"],["8cc9","顨杫䉶圽"],["8cce","藖𤥻芿𧄍䲁𦵴嵻𦬕𦾾龭龮宖龯曧繛湗秊㶈䓃𣉖𢞖䎚䔶"],["8ce6","峕𣬚諹屸㴒𣕑嵸龲煗䕘𤃬𡸣䱷㥸㑊𠆤𦱁諌侴𠈹妿腬顖𩣺弻"],["8d40","𠮟"],["8d42","𢇁𨥭䄂䚻𩁹㼇龳𪆵䃸㟖䛷𦱆䅼𨚲𧏿䕭㣔𥒚䕡䔛䶉䱻䵶䗪㿈𤬏㙡䓞䒽䇭崾嵈嵖㷼㠏嶤嶹㠠㠸幂庽弥徃㤈㤔㤿㥍惗愽峥㦉憷憹懏㦸戬抐拥挘㧸嚱"],["8da1","㨃揢揻搇摚㩋擀崕嘡龟㪗斆㪽旿晓㫲暒㬢朖㭂枤栀㭘桊梄㭲㭱㭻椉楃牜楤榟榅㮼槖㯝橥橴橱檂㯬檙㯲檫檵櫔櫶殁毁毪汵沪㳋洂洆洦涁㳯涤涱渕渘温溆𨧀溻滢滚齿滨滩漤漴㵆𣽁澁澾㵪㵵熷岙㶊瀬㶑灐灔灯灿炉𠌥䏁㗱𠻘"],["8e40","𣻗垾𦻓焾𥟠㙎榢𨯩孴穉𥣡𩓙穥穽𥦬窻窰竂竃燑𦒍䇊竚竝竪䇯咲𥰁笋筕笩𥌎𥳾箢筯莜𥮴𦱿篐萡箒箸𥴠㶭𥱥蒒篺簆簵𥳁籄粃𤢂粦晽𤕸糉糇糦籴糳糵糎"],["8ea1","繧䔝𦹄絝𦻖璍綉綫焵綳緒𤁗𦀩緤㴓緵𡟹緥𨍭縝𦄡𦅚繮纒䌫鑬縧罀罁罇礶𦋐駡羗𦍑羣𡙡𠁨䕜𣝦䔃𨌺翺𦒉者耈耝耨耯𪂇𦳃耻耼聡𢜔䦉𦘦𣷣𦛨朥肧𨩈脇脚墰𢛶汿𦒘𤾸擧𡒊舘𡡞橓𤩥𤪕䑺舩𠬍𦩒𣵾俹𡓽蓢荢𦬊𤦧𣔰𡝳𣷸芪椛芳䇛"],["8f40","蕋苐茚𠸖𡞴㛁𣅽𣕚艻苢茘𣺋𦶣𦬅𦮗𣗎㶿茝嗬莅䔋𦶥莬菁菓㑾𦻔橗蕚㒖𦹂𢻯葘𥯤葱㷓䓤檧葊𣲵祘蒨𦮖𦹷𦹃蓞萏莑䒠蒓蓤𥲑䉀𥳀䕃蔴嫲𦺙䔧蕳䔖枿蘖"],["8fa1","𨘥𨘻藁𧂈蘂𡖂𧃍䕫䕪蘨㙈𡢢号𧎚虾蝱𪃸蟮𢰧螱蟚蠏噡虬桖䘏衅衆𧗠𣶹𧗤衞袜䙛袴袵揁装睷𧜏覇覊覦覩覧覼𨨥觧𧤤𧪽誜瞓釾誐𧩙竩𧬺𣾏䜓𧬸煼謌謟𥐰𥕥謿譌譍誩𤩺讐讛誯𡛟䘕衏貛𧵔𧶏貫㜥𧵓賖𧶘𧶽贒贃𡤐賛灜贑𤳉㻐起"],["9040","趩𨀂𡀔𤦊㭼𨆼𧄌竧躭躶軃鋔輙輭𨍥𨐒辥錃𪊟𠩐辳䤪𨧞𨔽𣶻廸𣉢迹𪀔𨚼𨔁𢌥㦀𦻗逷𨔼𧪾遡𨕬𨘋邨𨜓郄𨛦邮都酧㫰醩釄粬𨤳𡺉鈎沟鉁鉢𥖹銹𨫆𣲛𨬌𥗛"],["90a1","𠴱錬鍫𨫡𨯫炏嫃𨫢𨫥䥥鉄𨯬𨰹𨯿鍳鑛躼閅閦鐦閠濶䊹𢙺𨛘𡉼𣸮䧟氜陻隖䅬隣𦻕懚隶磵𨫠隽双䦡𦲸𠉴𦐐𩂯𩃥𤫑𡤕𣌊霱虂霶䨏䔽䖅𤫩灵孁霛靜𩇕靗孊𩇫靟鐥僐𣂷𣂼鞉鞟鞱鞾韀韒韠𥑬韮琜𩐳響韵𩐝𧥺䫑頴頳顋顦㬎𧅵㵑𠘰𤅜"],["9140","𥜆飊颷飈飇䫿𦴧𡛓喰飡飦飬鍸餹𤨩䭲𩡗𩤅駵騌騻騐驘𥜥㛄𩂱𩯕髠髢𩬅髴䰎鬔鬭𨘀倴鬴𦦨㣃𣁽魐魀𩴾婅𡡣鮎𤉋鰂鯿鰌𩹨鷔𩾷𪆒𪆫𪃡𪄣𪇟鵾鶃𪄴鸎梈"],["91a1","鷄𢅛𪆓𪈠𡤻𪈳鴹𪂹𪊴麐麕麞麢䴴麪麯𤍤黁㭠㧥㴝伲㞾𨰫鼂鼈䮖鐤𦶢鼗鼖鼹嚟嚊齅馸𩂋韲葿齢齩竜龎爖䮾𤥵𤦻煷𤧸𤍈𤩑玞𨯚𡣺禟𨥾𨸶鍩鏳𨩄鋬鎁鏋𨥬𤒹爗㻫睲穃烐𤑳𤏸煾𡟯炣𡢾𣖙㻇𡢅𥐯𡟸㜢𡛻𡠹㛡𡝴𡣑𥽋㜣𡛀坛𤨥𡏾𡊨"],["9240","𡏆𡒶蔃𣚦蔃葕𤦔𧅥𣸱𥕜𣻻𧁒䓴𣛮𩦝𦼦柹㜳㰕㷧塬𡤢栐䁗𣜿𤃡𤂋𤄏𦰡哋嚞𦚱嚒𠿟𠮨𠸍鏆𨬓鎜仸儫㠙𤐶亼𠑥𠍿佋侊𥙑婨𠆫𠏋㦙𠌊𠐔㐵伩𠋀𨺳𠉵諚𠈌亘"],["92a1","働儍侢伃𤨎𣺊佂倮偬傁俌俥偘僼兙兛兝兞湶𣖕𣸹𣺿浲𡢄𣺉冨凃𠗠䓝𠒣𠒒𠒑赺𨪜𠜎剙劤𠡳勡鍮䙺熌𤎌𠰠𤦬𡃤槑𠸝瑹㻞璙琔瑖玘䮎𤪼𤂍叐㖄爏𤃉喴𠍅响𠯆圝鉝雴鍦埝垍坿㘾壋媙𨩆𡛺𡝯𡜐娬妸銏婾嫏娒𥥆𡧳𡡡𤊕㛵洅瑃娡𥺃"],["9340","媁𨯗𠐓鏠璌𡌃焅䥲鐈𨧻鎽㞠尞岞幞幈𡦖𡥼𣫮廍孏𡤃𡤄㜁𡢠㛝𡛾㛓脪𨩇𡶺𣑲𨦨弌弎𡤧𡞫婫𡜻孄蘔𧗽衠恾𢡠𢘫忛㺸𢖯𢖾𩂈𦽳懀𠀾𠁆𢘛憙憘恵𢲛𢴇𤛔𩅍"],["93a1","摱𤙥𢭪㨩𢬢𣑐𩣪𢹸挷𪑛撶挱揑𤧣𢵧护𢲡搻敫楲㯴𣂎𣊭𤦉𣊫唍𣋠𡣙𩐿曎𣊉𣆳㫠䆐𥖄𨬢𥖏𡛼𥕛𥐥磮𣄃𡠪𣈴㑤𣈏𣆂𤋉暎𦴤晫䮓昰𧡰𡷫晣𣋒𣋡昞𥡲㣑𣠺𣞼㮙𣞢𣏾瓐㮖枏𤘪梶栞㯄檾㡣𣟕𤒇樳橒櫉欅𡤒攑梘橌㯗橺歗𣿀𣲚鎠鋲𨯪𨫋"],["9440","銉𨀞𨧜鑧涥漋𤧬浧𣽿㶏渄𤀼娽渊塇洤硂焻𤌚𤉶烱牐犇犔𤞏𤜥兹𤪤𠗫瑺𣻸𣙟𤩊𤤗𥿡㼆㺱𤫟𨰣𣼵悧㻳瓌琼鎇琷䒟𦷪䕑疃㽣𤳙𤴆㽘畕癳𪗆㬙瑨𨫌𤦫𤦎㫻"],["94a1","㷍𤩎㻿𤧅𤣳釺圲鍂𨫣𡡤僟𥈡𥇧睸𣈲眎眏睻𤚗𣞁㩞𤣰琸璛㺿𤪺𤫇䃈𤪖𦆮錇𥖁砞碍碈磒珐祙𧝁𥛣䄎禛蒖禥樭𣻺稺秴䅮𡛦䄲鈵秱𠵌𤦌𠊙𣶺𡝮㖗啫㕰㚪𠇔𠰍竢婙𢛵𥪯𥪜娍𠉛磰娪𥯆竾䇹籝籭䈑𥮳𥺼𥺦糍𤧹𡞰粎籼粮檲緜縇緓罎𦉡"],["9540","𦅜𧭈綗𥺂䉪𦭵𠤖柖𠁎𣗏埄𦐒𦏸𤥢翝笧𠠬𥫩𥵃笌𥸎駦虅驣樜𣐿㧢𤧷𦖭騟𦖠蒀𧄧𦳑䓪脷䐂胆脉腂𦞴飃𦩂艢艥𦩑葓𦶧蘐𧈛媆䅿𡡀嬫𡢡嫤𡣘蚠蜨𣶏蠭𧐢娂"],["95a1","衮佅袇袿裦襥襍𥚃襔𧞅𧞄𨯵𨯙𨮜𨧹㺭蒣䛵䛏㟲訽訜𩑈彍鈫𤊄旔焩烄𡡅鵭貟賩𧷜妚矃姰䍮㛔踪躧𤰉輰轊䋴汘澻𢌡䢛潹溋𡟚鯩㚵𤤯邻邗啱䤆醻鐄𨩋䁢𨫼鐧𨰝𨰻蓥訫閙閧閗閖𨴴瑅㻂𤣿𤩂𤏪㻧𣈥随𨻧𨹦𨹥㻌𤧭𤩸𣿮琒瑫㻼靁𩂰"],["9640","桇䨝𩂓𥟟靝鍨𨦉𨰦𨬯𦎾銺嬑譩䤼珹𤈛鞛靱餸𠼦巁𨯅𤪲頟𩓚鋶𩗗釥䓀𨭐𤩧𨭤飜𨩅㼀鈪䤥萔餻饍𧬆㷽馛䭯馪驜𨭥𥣈檏騡嫾騯𩣱䮐𩥈馼䮽䮗鍽塲𡌂堢𤦸"],["96a1","𡓨硄𢜟𣶸棅㵽鑘㤧慐𢞁𢥫愇鱏鱓鱻鰵鰐魿鯏𩸭鮟𪇵𪃾鴡䲮𤄄鸘䲰鴌𪆴𪃭𪃳𩤯鶥蒽𦸒𦿟𦮂藼䔳𦶤𦺄𦷰萠藮𦸀𣟗𦁤秢𣖜𣙀䤭𤧞㵢鏛銾鍈𠊿碹鉷鑍俤㑀遤𥕝砽硔碶硋𡝗𣇉𤥁㚚佲濚濙瀞瀞吔𤆵垻壳垊鴖埗焴㒯𤆬燫𦱀𤾗嬨𡞵𨩉"],["9740","愌嫎娋䊼𤒈㜬䭻𨧼鎻鎸𡣖𠼝葲𦳀𡐓𤋺𢰦𤏁妔𣶷𦝁綨𦅛𦂤𤦹𤦋𨧺鋥珢㻩璴𨭣𡢟㻡𤪳櫘珳珻㻖𤨾𤪔𡟙𤩦𠎧𡐤𤧥瑈𤤖炥𤥶銄珦鍟𠓾錱𨫎𨨖鎆𨯧𥗕䤵𨪂煫"],["97a1","𤥃𠳿嚤𠘚𠯫𠲸唂秄𡟺緾𡛂𤩐𡡒䔮鐁㜊𨫀𤦭妰𡢿𡢃𧒄媡㛢𣵛㚰鉟婹𨪁𡡢鍴㳍𠪴䪖㦊僴㵩㵌𡎜煵䋻𨈘渏𩃤䓫浗𧹏灧沯㳖𣿭𣸭渂漌㵯𠏵畑㚼㓈䚀㻚䡱姄鉮䤾轁𨰜𦯀堒埈㛖𡑒烾𤍢𤩱𢿣𡊰𢎽梹楧𡎘𣓥𧯴𣛟𨪃𣟖𣏺𤲟樚𣚭𦲷萾䓟䓎"],["9840","𦴦𦵑𦲂𦿞漗𧄉茽𡜺菭𦲀𧁓𡟛妉媂𡞳婡婱𡤅𤇼㜭姯𡜼㛇熎鎐暚𤊥婮娫𤊓樫𣻹𧜶𤑛𤋊焝𤉙𨧡侰𦴨峂𤓎𧹍𤎽樌𤉖𡌄炦焳𤏩㶥泟勇𤩏繥姫崯㷳彜𤩝𡟟綤萦"],["98a1","咅𣫺𣌀𠈔坾𠣕𠘙㿥𡾞𪊶瀃𩅛嵰玏糓𨩙𩐠俈翧狍猐𧫴猸猹𥛶獁獈㺩𧬘遬燵𤣲珡臶㻊県㻑沢国琙琞琟㻢㻰㻴㻺瓓㼎㽓畂畭畲疍㽼痈痜㿀癍㿗癴㿜発𤽜熈嘣覀塩䀝睃䀹条䁅㗛瞘䁪䁯属瞾矋売砘点砜䂨砹硇硑硦葈𥔵礳栃礲䄃"],["9940","䄉禑禙辻稆込䅧窑䆲窼艹䇄竏竛䇏両筢筬筻簒簛䉠䉺类粜䊌粸䊔糭输烀𠳏総緔緐緽羮羴犟䎗耠耥笹耮耱联㷌垴炠肷胩䏭脌猪脎脒畠脔䐁㬹腖腙腚"],["99a1","䐓堺腼膄䐥膓䐭膥埯臁臤艔䒏芦艶苊苘苿䒰荗险榊萅烵葤惣蒈䔄蒾蓡蓸蔐蔸蕒䔻蕯蕰藠䕷虲蚒蚲蛯际螋䘆䘗袮裿褤襇覑𧥧訩訸誔誴豑賔賲贜䞘塟跃䟭仮踺嗘坔蹱嗵躰䠷軎転軤軭軲辷迁迊迌逳駄䢭飠鈓䤞鈨鉘鉫銱銮銿"],["9a40","鋣鋫鋳鋴鋽鍃鎄鎭䥅䥑麿鐗匁鐝鐭鐾䥪鑔鑹锭関䦧间阳䧥枠䨤靀䨵鞲韂噔䫤惨颹䬙飱塄餎餙冴餜餷饂饝饢䭰駅䮝騼鬏窃魩鮁鯝鯱鯴䱭鰠㝯𡯂鵉鰺"],["9aa1","黾噐鶓鶽鷀鷼银辶鹻麬麱麽黆铜黢黱黸竈齄𠂔𠊷𠎠椚铃妬𠓗塀铁㞹𠗕𠘕𠙶𡚺块煳𠫂𠫍𠮿呪吆𠯋咞𠯻𠰻𠱓𠱥𠱼惧𠲍噺𠲵𠳝𠳭𠵯𠶲𠷈楕鰯螥𠸄𠸎𠻗𠾐𠼭𠹳尠𠾼帋𡁜𡁏𡁶朞𡁻𡂈𡂖㙇𡂿𡃓𡄯𡄻卤蒭𡋣𡍵𡌶讁𡕷𡘙𡟃𡟇乸炻𡠭𡥪"],["9b40","𡨭𡩅𡰪𡱰𡲬𡻈拃𡻕𡼕熘桕𢁅槩㛈𢉼𢏗𢏺𢜪𢡱𢥏苽𢥧𢦓𢫕覥𢫨辠𢬎鞸𢬿顇骽𢱌"],["9b62","𢲈𢲷𥯨𢴈𢴒𢶷𢶕𢹂𢽴𢿌𣀳𣁦𣌟𣏞徱晈暿𧩹𣕧𣗳爁𤦺矗𣘚𣜖纇𠍆墵朎"],["9ba1","椘𣪧𧙗𥿢𣸑𣺹𧗾𢂚䣐䪸𤄙𨪚𤋮𤌍𤀻𤌴𤎖𤩅𠗊凒𠘑妟𡺨㮾𣳿𤐄𤓖垈𤙴㦛𤜯𨗨𩧉㝢𢇃譞𨭎駖𤠒𤣻𤨕爉𤫀𠱸奥𤺥𤾆𠝹軚𥀬劏圿煱𥊙𥐙𣽊𤪧喼𥑆𥑮𦭒釔㑳𥔿𧘲𥕞䜘𥕢𥕦𥟇𤤿𥡝偦㓻𣏌惞𥤃䝼𨥈𥪮𥮉𥰆𡶐垡煑澶𦄂𧰒遖𦆲𤾚譢𦐂𦑊"],["9c40","嵛𦯷輶𦒄𡤜諪𤧶𦒈𣿯𦔒䯀𦖿𦚵𢜛鑥𥟡憕娧晉侻嚹𤔡𦛼乪𤤴陖涏𦲽㘘襷𦞙𦡮𦐑𦡞營𦣇筂𩃀𠨑𦤦鄄𦤹穅鷰𦧺騦𦨭㙟𦑩𠀡禃𦨴𦭛崬𣔙菏𦮝䛐𦲤画补𦶮墶"],["9ca1","㜜𢖍𧁋𧇍㱔𧊀𧊅銁𢅺𧊋錰𧋦𤧐氹钟𧑐𠻸蠧裵𢤦𨑳𡞱溸𤨪𡠠㦤㚹尐秣䔿暶𩲭𩢤襃𧟌𧡘囖䃟𡘊㦡𣜯𨃨𡏅熭荦𧧝𩆨婧䲷𧂯𨦫𧧽𧨊𧬋𧵦𤅺筃祾𨀉澵𪋟樃𨌘厢𦸇鎿栶靝𨅯𨀣𦦵𡏭𣈯𨁈嶅𨰰𨂃圕頣𨥉嶫𤦈斾槕叒𤪥𣾁㰑朶𨂐𨃴𨄮𡾡𨅏"],["9d40","𨆉𨆯𨈚𨌆𨌯𨎊㗊𨑨𨚪䣺揦𨥖砈鉕𨦸䏲𨧧䏟𨧨𨭆𨯔姸𨰉輋𨿅𩃬筑𩄐𩄼㷷𩅞𤫊运犏嚋𩓧𩗩𩖰𩖸𩜲𩣑𩥉𩥪𩧃𩨨𩬎𩵚𩶛纟𩻸𩼣䲤镇𪊓熢𪋿䶑递𪗋䶜𠲜达嗁"],["9da1","辺𢒰边𤪓䔉繿潖檱仪㓤𨬬𧢝㜺躀𡟵𨀤𨭬𨮙𧨾𦚯㷫𧙕𣲷𥘵𥥖亚𥺁𦉘嚿𠹭踎孭𣺈𤲞揞拐𡟶𡡻攰嘭𥱊吚𥌑㷆𩶘䱽嘢嘞罉𥻘奵𣵀蝰东𠿪𠵉𣚺脗鵞贘瘻鱅癎瞹鍅吲腈苷嘥脲萘肽嗪祢噃吖𠺝㗎嘅嗱曱𨋢㘭甴嗰喺咗啲𠱁𠲖廐𥅈𠹶𢱢"],["9e40","𠺢麫絚嗞𡁵抝靭咔賍燶酶揼掹揾啩𢭃鱲𢺳冚㓟𠶧冧呍唞唓癦踭𦢊疱肶蠄螆裇膶萜𡃁䓬猄𤜆宐茋𦢓噻𢛴𧴯𤆣𧵳𦻐𧊶酰𡇙鈈𣳼𪚩𠺬𠻹牦𡲢䝎𤿂𧿹𠿫䃺"],["9ea1","鱝攟𢶠䣳𤟠𩵼𠿬𠸊恢𧖣𠿭"],["9ead","𦁈𡆇熣纎鵐业丄㕷嬍沲卧㚬㧜卽㚥𤘘墚𤭮舭呋垪𥪕𠥹"],["9ec5","㩒𢑥獴𩺬䴉鯭𣳾𩼰䱛𤾩𩖞𩿞葜𣶶𧊲𦞳𣜠挮紥𣻷𣸬㨪逈勌㹴㙺䗩𠒎癀嫰𠺶硺𧼮墧䂿噼鮋嵴癔𪐴麅䳡痹㟻愙𣃚𤏲"],["9ef5","噝𡊩垧𤥣𩸆刴𧂮㖭汊鵼"],["9f40","籖鬹埞𡝬屓擓𩓐𦌵𧅤蚭𠴨𦴢𤫢𠵱"],["9f4f","凾𡼏嶎霃𡷑麁遌笟鬂峑箣扨挵髿篏鬪籾鬮籂粆鰕篼鬉鼗鰛𤤾齚啳寃俽麘俲剠㸆勑坧偖妷帒韈鶫轜呩鞴饀鞺匬愰"],["9fa1","椬叚鰊鴂䰻陁榀傦畆𡝭駚剳"],["9fae","酙隁酜"],["9fb2","酑𨺗捿𦴣櫊嘑醎畺抅𠏼獏籰𥰡𣳽"],["9fc1","𤤙盖鮝个𠳔莾衂"],["9fc9","届槀僭坺刟巵从氱𠇲伹咜哚劚趂㗾弌㗳"],["9fdb","歒酼龥鮗頮颴骺麨麄煺笔"],["9fe7","毺蠘罸"],["9feb","嘠𪙊蹷齓"],["9ff0","跔蹏鸜踁抂𨍽踨蹵竓𤩷稾磘泪詧瘇"],["a040","𨩚鼦泎蟖痃𪊲硓咢贌狢獱謭猂瓱賫𤪻蘯徺袠䒷"],["a055","𡠻𦸅"],["a058","詾𢔛"],["a05b","惽癧髗鵄鍮鮏蟵"],["a063","蠏賷猬霡鮰㗖犲䰇籑饊𦅙慙䰄麖慽"],["a073","坟慯抦戹拎㩜懢厪𣏵捤栂㗒"],["a0a1","嵗𨯂迚𨸹"],["a0a6","僙𡵆礆匲阸𠼻䁥"],["a0ae","矾"],["a0b0","糂𥼚糚稭聦聣絍甅瓲覔舚朌聢𧒆聛瓰脃眤覉𦟌畓𦻑螩蟎臈螌詉貭譃眫瓸蓚㘵榲趦"],["a0d4","覩瑨涹蟁𤀑瓧㷛煶悤憜㳑煢恷"],["a0e2","罱𨬭牐惩䭾删㰘𣳇𥻗𧙖𥔱𡥄𡋾𩤃𦷜𧂭峁𦆭𨨏𣙷𠃮𦡆𤼎䕢嬟𦍌齐麦𦉫"],["a3c0","␀",31,"␡"],["c6a1","①",9,"⑴",9,"ⅰ",9,"丶丿亅亠冂冖冫勹匸卩厶夊宀巛⼳广廴彐彡攴无疒癶辵隶¨ˆヽヾゝゞ〃仝々〆〇ー［］✽ぁ",23],["c740","す",58,"ァアィイ"],["c7a1","ゥ",81,"А",5,"ЁЖ",4],["c840","Л",26,"ёж",25,"⇧↸↹㇏𠃌乚𠂊刂䒑"],["c8a1","龰冈龱𧘇"],["c8cd","￢￤＇＂㈱№℡゛゜⺀⺄⺆⺇⺈⺊⺌⺍⺕⺜⺝⺥⺧⺪⺬⺮⺶⺼⺾⻆⻊⻌⻍⻏⻖⻗⻞⻣"],["c8f5","ʃɐɛɔɵœøŋʊɪ"],["f9fe","￭"],["fa40","𠕇鋛𠗟𣿅蕌䊵珯况㙉𤥂𨧤鍄𡧛苮𣳈砼杄拟𤤳𨦪𠊠𦮳𡌅侫𢓭倈𦴩𧪄𣘀𤪱𢔓倩𠍾徤𠎀𠍇滛𠐟偽儁㑺儎顬㝃萖𤦤𠒇兠𣎴兪𠯿𢃼𠋥𢔰𠖎𣈳𡦃宂蝽𠖳𣲙冲冸"],["faa1","鴴凉减凑㳜凓𤪦决凢卂凭菍椾𣜭彻刋刦刼劵剗劔効勅簕蕂勠蘍𦬓包𨫞啉滙𣾀𠥔𣿬匳卄𠯢泋𡜦栛珕恊㺪㣌𡛨燝䒢卭却𨚫卾卿𡖖𡘓矦厓𨪛厠厫厮玧𥝲㽙玜叁叅汉义埾叙㪫𠮏叠𣿫𢶣叶𠱷吓灹唫晗浛呭𦭓𠵴啝咏咤䞦𡜍𠻝㶴𠵍"],["fb40","𨦼𢚘啇䳭启琗喆喩嘅𡣗𤀺䕒𤐵暳𡂴嘷曍𣊊暤暭噍噏磱囱鞇叾圀囯园𨭦㘣𡉏坆𤆥汮炋坂㚱𦱾埦𡐖堃𡑔𤍣堦𤯵塜墪㕡壠壜𡈼壻寿坃𪅐𤉸鏓㖡够梦㛃湙"],["fba1","𡘾娤啓𡚒蔅姉𠵎𦲁𦴪𡟜姙𡟻𡞲𦶦浱𡠨𡛕姹𦹅媫婣㛦𤦩婷㜈媖瑥嫓𦾡𢕔㶅𡤑㜲𡚸広勐孶斈孼𧨎䀄䡝𠈄寕慠𡨴𥧌𠖥寳宝䴐尅𡭄尓珎尔𡲥𦬨屉䣝岅峩峯嶋𡷹𡸷崐崘嵆𡺤岺巗苼㠭𤤁𢁉𢅳芇㠶㯂帮檊幵幺𤒼𠳓厦亷廐厨𡝱帉廴𨒂"],["fc40","廹廻㢠廼栾鐛弍𠇁弢㫞䢮𡌺强𦢈𢏐彘𢑱彣鞽𦹮彲鍀𨨶徧嶶㵟𥉐𡽪𧃸𢙨釖𠊞𨨩怱暅𡡷㥣㷇㘹垐𢞴祱㹀悞悤悳𤦂𤦏𧩓璤僡媠慤萤慂慈𦻒憁凴𠙖憇宪𣾷"],["fca1","𢡟懓𨮝𩥝懐㤲𢦀𢣁怣慜攞掋𠄘担𡝰拕𢸍捬𤧟㨗搸揸𡎎𡟼撐澊𢸶頔𤂌𥜝擡擥鑻㩦携㩗敍漖𤨨𤨣斅敭敟𣁾斵𤥀䬷旑䃘𡠩无旣忟𣐀昘𣇷𣇸晄𣆤𣆥晋𠹵晧𥇦晳晴𡸽𣈱𨗴𣇈𥌓矅𢣷馤朂𤎜𤨡㬫槺𣟂杞杧杢𤇍𩃭柗䓩栢湐鈼栁𣏦𦶠桝"],["fd40","𣑯槡樋𨫟楳棃𣗍椁椀㴲㨁𣘼㮀枬楡𨩊䋼椶榘㮡𠏉荣傐槹𣙙𢄪橅𣜃檝㯳枱櫈𩆜㰍欝𠤣惞欵歴𢟍溵𣫛𠎵𡥘㝀吡𣭚毡𣻼毜氷𢒋𤣱𦭑汚舦汹𣶼䓅𣶽𤆤𤤌𤤀"],["fda1","𣳉㛥㳫𠴲鮃𣇹𢒑羏样𦴥𦶡𦷫涖浜湼漄𤥿𤂅𦹲蔳𦽴凇沜渝萮𨬡港𣸯瑓𣾂秌湏媑𣁋濸㜍澝𣸰滺𡒗𤀽䕕鏰潄潜㵎潴𩅰㴻澟𤅄濓𤂑𤅕𤀹𣿰𣾴𤄿凟𤅖𤅗𤅀𦇝灋灾炧炁烌烕烖烟䄄㷨熴熖𤉷焫煅媈煊煮岜𤍥煏鍢𤋁焬𤑚𤨧𤨢熺𨯨炽爎"],["fe40","鑂爕夑鑃爤鍁𥘅爮牀𤥴梽牕牗㹕𣁄栍漽犂猪猫𤠣𨠫䣭𨠄猨献珏玪𠰺𦨮珉瑉𤇢𡛧𤨤昣㛅𤦷𤦍𤧻珷琕椃𤨦琹𠗃㻗瑜𢢭瑠𨺲瑇珤瑶莹瑬㜰瑴鏱樬璂䥓𤪌"],["fea1","𤅟𤩹𨮏孆𨰃𡢞瓈𡦈甎瓩甞𨻙𡩋寗𨺬鎅畍畊畧畮𤾂㼄𤴓疎瑝疞疴瘂瘬癑癏癯癶𦏵皐臯㟸𦤑𦤎皡皥皷盌𦾟葢𥂝𥅽𡸜眞眦着撯𥈠睘𣊬瞯𨥤𨥨𡛁矴砉𡍶𤨒棊碯磇磓隥礮𥗠磗礴碱𧘌辸袄𨬫𦂃𢘜禆褀椂禀𥡗禝𧬹礼禩渪𧄦㺨秆𩄍秔"]]');

/***/ }),

/***/ 74488:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["0","\\u0000",127,"€"],["8140","丂丄丅丆丏丒丗丟丠両丣並丩丮丯丱丳丵丷丼乀乁乂乄乆乊乑乕乗乚乛乢乣乤乥乧乨乪",5,"乲乴",9,"乿",6,"亇亊"],["8180","亐亖亗亙亜亝亞亣亪亯亰亱亴亶亷亸亹亼亽亾仈仌仏仐仒仚仛仜仠仢仦仧仩仭仮仯仱仴仸仹仺仼仾伀伂",6,"伋伌伒",4,"伜伝伡伣伨伩伬伭伮伱伳伵伷伹伻伾",4,"佄佅佇",5,"佒佔佖佡佢佦佨佪佫佭佮佱佲併佷佸佹佺佽侀侁侂侅來侇侊侌侎侐侒侓侕侖侘侙侚侜侞侟価侢"],["8240","侤侫侭侰",4,"侶",8,"俀俁係俆俇俈俉俋俌俍俒",4,"俙俛俠俢俤俥俧俫俬俰俲俴俵俶俷俹俻俼俽俿",11],["8280","個倎倐們倓倕倖倗倛倝倞倠倢倣値倧倫倯",10,"倻倽倿偀偁偂偄偅偆偉偊偋偍偐",4,"偖偗偘偙偛偝",7,"偦",5,"偭",8,"偸偹偺偼偽傁傂傃傄傆傇傉傊傋傌傎",20,"傤傦傪傫傭",4,"傳",6,"傼"],["8340","傽",17,"僐",5,"僗僘僙僛",10,"僨僩僪僫僯僰僱僲僴僶",4,"僼",9,"儈"],["8380","儉儊儌",5,"儓",13,"儢",28,"兂兇兊兌兎兏児兒兓兗兘兙兛兝",4,"兣兤兦內兩兪兯兲兺兾兿冃冄円冇冊冋冎冏冐冑冓冔冘冚冝冞冟冡冣冦",4,"冭冮冴冸冹冺冾冿凁凂凃凅凈凊凍凎凐凒",5],["8440","凘凙凚凜凞凟凢凣凥",5,"凬凮凱凲凴凷凾刄刅刉刋刌刏刐刓刔刕刜刞刟刡刢刣別刦刧刪刬刯刱刲刴刵刼刾剄",5,"剋剎剏剒剓剕剗剘"],["8480","剙剚剛剝剟剠剢剣剤剦剨剫剬剭剮剰剱剳",9,"剾劀劃",4,"劉",6,"劑劒劔",6,"劜劤劥劦劧劮劯劰労",9,"勀勁勂勄勅勆勈勊勌勍勎勏勑勓勔動勗務",5,"勠勡勢勣勥",10,"勱",7,"勻勼勽匁匂匃匄匇匉匊匋匌匎"],["8540","匑匒匓匔匘匛匜匞匟匢匤匥匧匨匩匫匬匭匯",9,"匼匽區卂卄卆卋卌卍卐協単卙卛卝卥卨卪卬卭卲卶卹卻卼卽卾厀厁厃厇厈厊厎厏"],["8580","厐",4,"厖厗厙厛厜厞厠厡厤厧厪厫厬厭厯",6,"厷厸厹厺厼厽厾叀參",4,"収叏叐叒叓叕叚叜叝叞叡叢叧叴叺叾叿吀吂吅吇吋吔吘吙吚吜吢吤吥吪吰吳吶吷吺吽吿呁呂呄呅呇呉呌呍呎呏呑呚呝",4,"呣呥呧呩",7,"呴呹呺呾呿咁咃咅咇咈咉咊咍咑咓咗咘咜咞咟咠咡"],["8640","咢咥咮咰咲咵咶咷咹咺咼咾哃哅哊哋哖哘哛哠",4,"哫哬哯哰哱哴",5,"哻哾唀唂唃唄唅唈唊",4,"唒唓唕",5,"唜唝唞唟唡唥唦"],["8680","唨唩唫唭唲唴唵唶唸唹唺唻唽啀啂啅啇啈啋",4,"啑啒啓啔啗",4,"啝啞啟啠啢啣啨啩啫啯",5,"啹啺啽啿喅喆喌喍喎喐喒喓喕喖喗喚喛喞喠",6,"喨",8,"喲喴営喸喺喼喿",4,"嗆嗇嗈嗊嗋嗎嗏嗐嗕嗗",4,"嗞嗠嗢嗧嗩嗭嗮嗰嗱嗴嗶嗸",4,"嗿嘂嘃嘄嘅"],["8740","嘆嘇嘊嘋嘍嘐",7,"嘙嘚嘜嘝嘠嘡嘢嘥嘦嘨嘩嘪嘫嘮嘯嘰嘳嘵嘷嘸嘺嘼嘽嘾噀",11,"噏",4,"噕噖噚噛噝",4],["8780","噣噥噦噧噭噮噯噰噲噳噴噵噷噸噹噺噽",7,"嚇",6,"嚐嚑嚒嚔",14,"嚤",10,"嚰",6,"嚸嚹嚺嚻嚽",12,"囋",8,"囕囖囘囙囜団囥",5,"囬囮囯囲図囶囷囸囻囼圀圁圂圅圇國",6],["8840","園",9,"圝圞圠圡圢圤圥圦圧圫圱圲圴",4,"圼圽圿坁坃坄坅坆坈坉坋坒",4,"坘坙坢坣坥坧坬坮坰坱坲坴坵坸坹坺坽坾坿垀"],["8880","垁垇垈垉垊垍",4,"垔",6,"垜垝垞垟垥垨垪垬垯垰垱垳垵垶垷垹",8,"埄",6,"埌埍埐埑埓埖埗埛埜埞埡埢埣埥",7,"埮埰埱埲埳埵埶執埻埼埾埿堁堃堄堅堈堉堊堌堎堏堐堒堓堔堖堗堘堚堛堜堝堟堢堣堥",4,"堫",4,"報堲堳場堶",7],["8940","堾",5,"塅",6,"塎塏塐塒塓塕塖塗塙",4,"塟",5,"塦",4,"塭",16,"塿墂墄墆墇墈墊墋墌"],["8980","墍",4,"墔",4,"墛墜墝墠",7,"墪",17,"墽墾墿壀壂壃壄壆",10,"壒壓壔壖",13,"壥",5,"壭壯壱売壴壵壷壸壺",7,"夃夅夆夈",4,"夎夐夑夒夓夗夘夛夝夞夠夡夢夣夦夨夬夰夲夳夵夶夻"],["8a40","夽夾夿奀奃奅奆奊奌奍奐奒奓奙奛",4,"奡奣奤奦",12,"奵奷奺奻奼奾奿妀妅妉妋妌妎妏妐妑妔妕妘妚妛妜妝妟妠妡妢妦"],["8a80","妧妬妭妰妱妳",5,"妺妼妽妿",6,"姇姈姉姌姍姎姏姕姖姙姛姞",4,"姤姦姧姩姪姫姭",11,"姺姼姽姾娀娂娊娋娍娎娏娐娒娔娕娖娗娙娚娛娝娞娡娢娤娦娧娨娪",6,"娳娵娷",4,"娽娾娿婁",4,"婇婈婋",9,"婖婗婘婙婛",5],["8b40","婡婣婤婥婦婨婩婫",8,"婸婹婻婼婽婾媀",17,"媓",6,"媜",13,"媫媬"],["8b80","媭",4,"媴媶媷媹",4,"媿嫀嫃",5,"嫊嫋嫍",4,"嫓嫕嫗嫙嫚嫛嫝嫞嫟嫢嫤嫥嫧嫨嫪嫬",4,"嫲",22,"嬊",11,"嬘",25,"嬳嬵嬶嬸",7,"孁",6],["8c40","孈",7,"孒孖孞孠孡孧孨孫孭孮孯孲孴孶孷學孹孻孼孾孿宂宆宊宍宎宐宑宒宔宖実宧宨宩宬宭宮宯宱宲宷宺宻宼寀寁寃寈寉寊寋寍寎寏"],["8c80","寑寔",8,"寠寢寣實寧審",4,"寯寱",6,"寽対尀専尃尅將專尋尌對導尐尒尓尗尙尛尞尟尠尡尣尦尨尩尪尫尭尮尯尰尲尳尵尶尷屃屄屆屇屌屍屒屓屔屖屗屘屚屛屜屝屟屢層屧",6,"屰屲",6,"屻屼屽屾岀岃",4,"岉岊岋岎岏岒岓岕岝",4,"岤",4],["8d40","岪岮岯岰岲岴岶岹岺岻岼岾峀峂峃峅",5,"峌",5,"峓",5,"峚",6,"峢峣峧峩峫峬峮峯峱",9,"峼",4],["8d80","崁崄崅崈",5,"崏",4,"崕崗崘崙崚崜崝崟",4,"崥崨崪崫崬崯",4,"崵",7,"崿",7,"嵈嵉嵍",10,"嵙嵚嵜嵞",10,"嵪嵭嵮嵰嵱嵲嵳嵵",12,"嶃",21,"嶚嶛嶜嶞嶟嶠"],["8e40","嶡",21,"嶸",12,"巆",6,"巎",12,"巜巟巠巣巤巪巬巭"],["8e80","巰巵巶巸",4,"巿帀帄帇帉帊帋帍帎帒帓帗帞",7,"帨",4,"帯帰帲",4,"帹帺帾帿幀幁幃幆",5,"幍",6,"幖",4,"幜幝幟幠幣",14,"幵幷幹幾庁庂広庅庈庉庌庍庎庒庘庛庝庡庢庣庤庨",4,"庮",4,"庴庺庻庼庽庿",6],["8f40","廆廇廈廋",5,"廔廕廗廘廙廚廜",11,"廩廫",8,"廵廸廹廻廼廽弅弆弇弉弌弍弎弐弒弔弖弙弚弜弝弞弡弢弣弤"],["8f80","弨弫弬弮弰弲",6,"弻弽弾弿彁",14,"彑彔彙彚彛彜彞彟彠彣彥彧彨彫彮彯彲彴彵彶彸彺彽彾彿徃徆徍徎徏徑従徔徖徚徛徝從徟徠徢",5,"復徫徬徯",5,"徶徸徹徺徻徾",4,"忇忈忊忋忎忓忔忕忚忛応忞忟忢忣忥忦忨忩忬忯忰忲忳忴忶忷忹忺忼怇"],["9040","怈怉怋怌怐怑怓怗怘怚怞怟怢怣怤怬怭怮怰",4,"怶",4,"怽怾恀恄",6,"恌恎恏恑恓恔恖恗恘恛恜恞恟恠恡恥恦恮恱恲恴恵恷恾悀"],["9080","悁悂悅悆悇悈悊悋悎悏悐悑悓悕悗悘悙悜悞悡悢悤悥悧悩悪悮悰悳悵悶悷悹悺悽",7,"惇惈惉惌",4,"惒惓惔惖惗惙惛惞惡",4,"惪惱惲惵惷惸惻",4,"愂愃愄愅愇愊愋愌愐",4,"愖愗愘愙愛愜愝愞愡愢愥愨愩愪愬",18,"慀",6],["9140","慇慉態慍慏慐慒慓慔慖",6,"慞慟慠慡慣慤慥慦慩",6,"慱慲慳慴慶慸",18,"憌憍憏",4,"憕"],["9180","憖",6,"憞",8,"憪憫憭",9,"憸",5,"憿懀懁懃",4,"應懌",4,"懓懕",16,"懧",13,"懶",8,"戀",5,"戇戉戓戔戙戜戝戞戠戣戦戧戨戩戫戭戯戰戱戲戵戶戸",4,"扂扄扅扆扊"],["9240","扏扐払扖扗扙扚扜",6,"扤扥扨扱扲扴扵扷扸扺扻扽抁抂抃抅抆抇抈抋",5,"抔抙抜抝択抣抦抧抩抪抭抮抯抰抲抳抴抶抷抸抺抾拀拁"],["9280","拃拋拏拑拕拝拞拠拡拤拪拫拰拲拵拸拹拺拻挀挃挄挅挆挊挋挌挍挏挐挒挓挔挕挗挘挙挜挦挧挩挬挭挮挰挱挳",5,"挻挼挾挿捀捁捄捇捈捊捑捒捓捔捖",7,"捠捤捥捦捨捪捫捬捯捰捲捳捴捵捸捹捼捽捾捿掁掃掄掅掆掋掍掑掓掔掕掗掙",6,"採掤掦掫掯掱掲掵掶掹掻掽掿揀"],["9340","揁揂揃揅揇揈揊揋揌揑揓揔揕揗",6,"揟揢揤",4,"揫揬揮揯揰揱揳揵揷揹揺揻揼揾搃搄搆",4,"損搎搑搒搕",5,"搝搟搢搣搤"],["9380","搥搧搨搩搫搮",5,"搵",4,"搻搼搾摀摂摃摉摋",6,"摓摕摖摗摙",4,"摟",7,"摨摪摫摬摮",9,"摻",6,"撃撆撈",8,"撓撔撗撘撚撛撜撝撟",4,"撥撦撧撨撪撫撯撱撲撳撴撶撹撻撽撾撿擁擃擄擆",6,"擏擑擓擔擕擖擙據"],["9440","擛擜擝擟擠擡擣擥擧",24,"攁",7,"攊",7,"攓",4,"攙",8],["9480","攢攣攤攦",4,"攬攭攰攱攲攳攷攺攼攽敀",4,"敆敇敊敋敍敎敐敒敓敔敗敘敚敜敟敠敡敤敥敧敨敩敪敭敮敯敱敳敵敶數",14,"斈斉斊斍斎斏斒斔斕斖斘斚斝斞斠斢斣斦斨斪斬斮斱",7,"斺斻斾斿旀旂旇旈旉旊旍旐旑旓旔旕旘",7,"旡旣旤旪旫"],["9540","旲旳旴旵旸旹旻",4,"昁昄昅昇昈昉昋昍昐昑昒昖昗昘昚昛昜昞昡昢昣昤昦昩昪昫昬昮昰昲昳昷",4,"昽昿晀時晄",6,"晍晎晐晑晘"],["9580","晙晛晜晝晞晠晢晣晥晧晩",4,"晱晲晳晵晸晹晻晼晽晿暀暁暃暅暆暈暉暊暋暍暎暏暐暒暓暔暕暘",4,"暞",8,"暩",4,"暯",4,"暵暶暷暸暺暻暼暽暿",25,"曚曞",7,"曧曨曪",5,"曱曵曶書曺曻曽朁朂會"],["9640","朄朅朆朇朌朎朏朑朒朓朖朘朙朚朜朞朠",5,"朧朩朮朰朲朳朶朷朸朹朻朼朾朿杁杄杅杇杊杋杍杒杔杕杗",4,"杝杢杣杤杦杧杫杬杮東杴杶"],["9680","杸杹杺杻杽枀枂枃枅枆枈枊枌枍枎枏枑枒枓枔枖枙枛枟枠枡枤枦枩枬枮枱枲枴枹",7,"柂柅",9,"柕柖柗柛柟柡柣柤柦柧柨柪柫柭柮柲柵",7,"柾栁栂栃栄栆栍栐栒栔栕栘",4,"栞栟栠栢",6,"栫",6,"栴栵栶栺栻栿桇桋桍桏桒桖",5],["9740","桜桝桞桟桪桬",7,"桵桸",8,"梂梄梇",7,"梐梑梒梔梕梖梘",9,"梣梤梥梩梪梫梬梮梱梲梴梶梷梸"],["9780","梹",6,"棁棃",5,"棊棌棎棏棐棑棓棔棖棗棙棛",4,"棡棢棤",9,"棯棲棳棴棶棷棸棻棽棾棿椀椂椃椄椆",4,"椌椏椑椓",11,"椡椢椣椥",7,"椮椯椱椲椳椵椶椷椸椺椻椼椾楀楁楃",16,"楕楖楘楙楛楜楟"],["9840","楡楢楤楥楧楨楩楪楬業楯楰楲",4,"楺楻楽楾楿榁榃榅榊榋榌榎",5,"榖榗榙榚榝",9,"榩榪榬榮榯榰榲榳榵榶榸榹榺榼榽"],["9880","榾榿槀槂",7,"構槍槏槑槒槓槕",5,"槜槝槞槡",11,"槮槯槰槱槳",9,"槾樀",9,"樋",11,"標",5,"樠樢",5,"権樫樬樭樮樰樲樳樴樶",6,"樿",4,"橅橆橈",7,"橑",6,"橚"],["9940","橜",4,"橢橣橤橦",10,"橲",6,"橺橻橽橾橿檁檂檃檅",8,"檏檒",4,"檘",7,"檡",5],["9980","檧檨檪檭",114,"欥欦欨",6],["9a40","欯欰欱欳欴欵欶欸欻欼欽欿歀歁歂歄歅歈歊歋歍",11,"歚",7,"歨歩歫",13,"歺歽歾歿殀殅殈"],["9a80","殌殎殏殐殑殔殕殗殘殙殜",4,"殢",7,"殫",7,"殶殸",6,"毀毃毄毆",4,"毌毎毐毑毘毚毜",4,"毢",7,"毬毭毮毰毱毲毴毶毷毸毺毻毼毾",6,"氈",4,"氎氒気氜氝氞氠氣氥氫氬氭氱氳氶氷氹氺氻氼氾氿汃汄汅汈汋",4,"汑汒汓汖汘"],["9b40","汙汚汢汣汥汦汧汫",4,"汱汳汵汷汸決汻汼汿沀沄沇沊沋沍沎沑沒沕沖沗沘沚沜沝沞沠沢沨沬沯沰沴沵沶沷沺泀況泂泃泆泇泈泋泍泎泏泑泒泘"],["9b80","泙泚泜泝泟泤泦泧泩泬泭泲泴泹泿洀洂洃洅洆洈洉洊洍洏洐洑洓洔洕洖洘洜洝洟",5,"洦洨洩洬洭洯洰洴洶洷洸洺洿浀浂浄浉浌浐浕浖浗浘浛浝浟浡浢浤浥浧浨浫浬浭浰浱浲浳浵浶浹浺浻浽",4,"涃涄涆涇涊涋涍涏涐涒涖",4,"涜涢涥涬涭涰涱涳涴涶涷涹",5,"淁淂淃淈淉淊"],["9c40","淍淎淏淐淒淓淔淕淗淚淛淜淟淢淣淥淧淨淩淪淭淯淰淲淴淵淶淸淺淽",7,"渆渇済渉渋渏渒渓渕渘渙減渜渞渟渢渦渧渨渪測渮渰渱渳渵"],["9c80","渶渷渹渻",7,"湅",7,"湏湐湑湒湕湗湙湚湜湝湞湠",10,"湬湭湯",14,"満溁溂溄溇溈溊",4,"溑",6,"溙溚溛溝溞溠溡溣溤溦溨溩溫溬溭溮溰溳溵溸溹溼溾溿滀滃滄滅滆滈滉滊滌滍滎滐滒滖滘滙滛滜滝滣滧滪",5],["9d40","滰滱滲滳滵滶滷滸滺",7,"漃漄漅漇漈漊",4,"漐漑漒漖",9,"漡漢漣漥漦漧漨漬漮漰漲漴漵漷",6,"漿潀潁潂"],["9d80","潃潄潅潈潉潊潌潎",9,"潙潚潛潝潟潠潡潣潤潥潧",5,"潯潰潱潳潵潶潷潹潻潽",6,"澅澆澇澊澋澏",12,"澝澞澟澠澢",4,"澨",10,"澴澵澷澸澺",5,"濁濃",5,"濊",6,"濓",10,"濟濢濣濤濥"],["9e40","濦",7,"濰",32,"瀒",7,"瀜",6,"瀤",6],["9e80","瀫",9,"瀶瀷瀸瀺",17,"灍灎灐",13,"灟",11,"灮灱灲灳灴灷灹灺灻災炁炂炃炄炆炇炈炋炌炍炏炐炑炓炗炘炚炛炞",12,"炰炲炴炵炶為炾炿烄烅烆烇烉烋",12,"烚"],["9f40","烜烝烞烠烡烢烣烥烪烮烰",6,"烸烺烻烼烾",10,"焋",4,"焑焒焔焗焛",10,"焧",7,"焲焳焴"],["9f80","焵焷",13,"煆煇煈煉煋煍煏",12,"煝煟",4,"煥煩",4,"煯煰煱煴煵煶煷煹煻煼煾",5,"熅",4,"熋熌熍熎熐熑熒熓熕熖熗熚",4,"熡",6,"熩熪熫熭",5,"熴熶熷熸熺",8,"燄",9,"燏",4],["a040","燖",9,"燡燢燣燤燦燨",5,"燯",9,"燺",11,"爇",19],["a080","爛爜爞",9,"爩爫爭爮爯爲爳爴爺爼爾牀",6,"牉牊牋牎牏牐牑牓牔牕牗牘牚牜牞牠牣牤牥牨牪牫牬牭牰牱牳牴牶牷牸牻牼牽犂犃犅",4,"犌犎犐犑犓",11,"犠",11,"犮犱犲犳犵犺",6,"狅狆狇狉狊狋狌狏狑狓狔狕狖狘狚狛"],["a1a1","　、。·ˉˇ¨〃々—～‖…‘’“”〔〕〈",7,"〖〗【】±×÷∶∧∨∑∏∪∩∈∷√⊥∥∠⌒⊙∫∮≡≌≈∽∝≠≮≯≤≥∞∵∴♂♀°′″℃＄¤￠￡‰§№☆★○●◎◇◆□■△▲※→←↑↓〓"],["a2a1","ⅰ",9],["a2b1","⒈",19,"⑴",19,"①",9],["a2e5","㈠",9],["a2f1","Ⅰ",11],["a3a1","！＂＃￥％",88,"￣"],["a4a1","ぁ",82],["a5a1","ァ",85],["a6a1","Α",16,"Σ",6],["a6c1","α",16,"σ",6],["a6e0","︵︶︹︺︿﹀︽︾﹁﹂﹃﹄"],["a6ee","︻︼︷︸︱"],["a6f4","︳︴"],["a7a1","А",5,"ЁЖ",25],["a7d1","а",5,"ёж",25],["a840","ˊˋ˙–―‥‵℅℉↖↗↘↙∕∟∣≒≦≧⊿═",35,"▁",6],["a880","█",7,"▓▔▕▼▽◢◣◤◥☉⊕〒〝〞"],["a8a1","āáǎàēéěèīíǐìōóǒòūúǔùǖǘǚǜüêɑ"],["a8bd","ńň"],["a8c0","ɡ"],["a8c5","ㄅ",36],["a940","〡",8,"㊣㎎㎏㎜㎝㎞㎡㏄㏎㏑㏒㏕︰￢￤"],["a959","℡㈱"],["a95c","‐"],["a960","ー゛゜ヽヾ〆ゝゞ﹉",9,"﹔﹕﹖﹗﹙",8],["a980","﹢",4,"﹨﹩﹪﹫"],["a996","〇"],["a9a4","─",75],["aa40","狜狝狟狢",5,"狪狫狵狶狹狽狾狿猀猂猄",5,"猋猌猍猏猐猑猒猔猘猙猚猟猠猣猤猦猧猨猭猯猰猲猳猵猶猺猻猼猽獀",8],["aa80","獉獊獋獌獎獏獑獓獔獕獖獘",7,"獡",10,"獮獰獱"],["ab40","獲",11,"獿",4,"玅玆玈玊玌玍玏玐玒玓玔玕玗玘玙玚玜玝玞玠玡玣",5,"玪玬玭玱玴玵玶玸玹玼玽玾玿珁珃",4],["ab80","珋珌珎珒",6,"珚珛珜珝珟珡珢珣珤珦珨珪珫珬珮珯珰珱珳",4],["ac40","珸",10,"琄琇琈琋琌琍琎琑",8,"琜",5,"琣琤琧琩琫琭琯琱琲琷",4,"琽琾琿瑀瑂",11],["ac80","瑎",6,"瑖瑘瑝瑠",12,"瑮瑯瑱",4,"瑸瑹瑺"],["ad40","瑻瑼瑽瑿璂璄璅璆璈璉璊璌璍璏璑",10,"璝璟",7,"璪",15,"璻",12],["ad80","瓈",9,"瓓",8,"瓝瓟瓡瓥瓧",6,"瓰瓱瓲"],["ae40","瓳瓵瓸",6,"甀甁甂甃甅",7,"甎甐甒甔甕甖甗甛甝甞甠",4,"甦甧甪甮甴甶甹甼甽甿畁畂畃畄畆畇畉畊畍畐畑畒畓畕畖畗畘"],["ae80","畝",7,"畧畨畩畫",6,"畳畵當畷畺",4,"疀疁疂疄疅疇"],["af40","疈疉疊疌疍疎疐疓疕疘疛疜疞疢疦",4,"疭疶疷疺疻疿痀痁痆痋痌痎痏痐痑痓痗痙痚痜痝痟痠痡痥痩痬痭痮痯痲痳痵痶痷痸痺痻痽痾瘂瘄瘆瘇"],["af80","瘈瘉瘋瘍瘎瘏瘑瘒瘓瘔瘖瘚瘜瘝瘞瘡瘣瘧瘨瘬瘮瘯瘱瘲瘶瘷瘹瘺瘻瘽癁療癄"],["b040","癅",6,"癎",5,"癕癗",4,"癝癟癠癡癢癤",6,"癬癭癮癰",7,"癹発發癿皀皁皃皅皉皊皌皍皏皐皒皔皕皗皘皚皛"],["b080","皜",7,"皥",8,"皯皰皳皵",9,"盀盁盃啊阿埃挨哎唉哀皑癌蔼矮艾碍爱隘鞍氨安俺按暗岸胺案肮昂盎凹敖熬翱袄傲奥懊澳芭捌扒叭吧笆八疤巴拔跋靶把耙坝霸罢爸白柏百摆佰败拜稗斑班搬扳般颁板版扮拌伴瓣半办绊邦帮梆榜膀绑棒磅蚌镑傍谤苞胞包褒剥"],["b140","盄盇盉盋盌盓盕盙盚盜盝盞盠",4,"盦",7,"盰盳盵盶盷盺盻盽盿眀眂眃眅眆眊県眎",10,"眛眜眝眞眡眣眤眥眧眪眫"],["b180","眬眮眰",4,"眹眻眽眾眿睂睄睅睆睈",7,"睒",7,"睜薄雹保堡饱宝抱报暴豹鲍爆杯碑悲卑北辈背贝钡倍狈备惫焙被奔苯本笨崩绷甭泵蹦迸逼鼻比鄙笔彼碧蓖蔽毕毙毖币庇痹闭敝弊必辟壁臂避陛鞭边编贬扁便变卞辨辩辫遍标彪膘表鳖憋别瘪彬斌濒滨宾摈兵冰柄丙秉饼炳"],["b240","睝睞睟睠睤睧睩睪睭",11,"睺睻睼瞁瞂瞃瞆",5,"瞏瞐瞓",11,"瞡瞣瞤瞦瞨瞫瞭瞮瞯瞱瞲瞴瞶",4],["b280","瞼瞾矀",12,"矎",8,"矘矙矚矝",4,"矤病并玻菠播拨钵波博勃搏铂箔伯帛舶脖膊渤泊驳捕卜哺补埠不布步簿部怖擦猜裁材才财睬踩采彩菜蔡餐参蚕残惭惨灿苍舱仓沧藏操糙槽曹草厕策侧册测层蹭插叉茬茶查碴搽察岔差诧拆柴豺搀掺蝉馋谗缠铲产阐颤昌猖"],["b340","矦矨矪矯矰矱矲矴矵矷矹矺矻矼砃",5,"砊砋砎砏砐砓砕砙砛砞砠砡砢砤砨砪砫砮砯砱砲砳砵砶砽砿硁硂硃硄硆硈硉硊硋硍硏硑硓硔硘硙硚"],["b380","硛硜硞",11,"硯",7,"硸硹硺硻硽",6,"场尝常长偿肠厂敞畅唱倡超抄钞朝嘲潮巢吵炒车扯撤掣彻澈郴臣辰尘晨忱沉陈趁衬撑称城橙成呈乘程惩澄诚承逞骋秤吃痴持匙池迟弛驰耻齿侈尺赤翅斥炽充冲虫崇宠抽酬畴踌稠愁筹仇绸瞅丑臭初出橱厨躇锄雏滁除楚"],["b440","碄碅碆碈碊碋碏碐碒碔碕碖碙碝碞碠碢碤碦碨",7,"碵碶碷碸確碻碼碽碿磀磂磃磄磆磇磈磌磍磎磏磑磒磓磖磗磘磚",9],["b480","磤磥磦磧磩磪磫磭",4,"磳磵磶磸磹磻",5,"礂礃礄礆",6,"础储矗搐触处揣川穿椽传船喘串疮窗幢床闯创吹炊捶锤垂春椿醇唇淳纯蠢戳绰疵茨磁雌辞慈瓷词此刺赐次聪葱囱匆从丛凑粗醋簇促蹿篡窜摧崔催脆瘁粹淬翠村存寸磋撮搓措挫错搭达答瘩打大呆歹傣戴带殆代贷袋待逮"],["b540","礍",5,"礔",9,"礟",4,"礥",14,"礵",4,"礽礿祂祃祄祅祇祊",8,"祔祕祘祙祡祣"],["b580","祤祦祩祪祫祬祮祰",6,"祹祻",4,"禂禃禆禇禈禉禋禌禍禎禐禑禒怠耽担丹单郸掸胆旦氮但惮淡诞弹蛋当挡党荡档刀捣蹈倒岛祷导到稻悼道盗德得的蹬灯登等瞪凳邓堤低滴迪敌笛狄涤翟嫡抵底地蒂第帝弟递缔颠掂滇碘点典靛垫电佃甸店惦奠淀殿碉叼雕凋刁掉吊钓调跌爹碟蝶迭谍叠"],["b640","禓",6,"禛",11,"禨",10,"禴",4,"禼禿秂秄秅秇秈秊秌秎秏秐秓秔秖秗秙",5,"秠秡秢秥秨秪"],["b680","秬秮秱",6,"秹秺秼秾秿稁稄稅稇稈稉稊稌稏",4,"稕稖稘稙稛稜丁盯叮钉顶鼎锭定订丢东冬董懂动栋侗恫冻洞兜抖斗陡豆逗痘都督毒犊独读堵睹赌杜镀肚度渡妒端短锻段断缎堆兑队对墩吨蹲敦顿囤钝盾遁掇哆多夺垛躲朵跺舵剁惰堕蛾峨鹅俄额讹娥恶厄扼遏鄂饿恩而儿耳尔饵洱二"],["b740","稝稟稡稢稤",14,"稴稵稶稸稺稾穀",5,"穇",9,"穒",4,"穘",16],["b780","穩",6,"穱穲穳穵穻穼穽穾窂窅窇窉窊窋窌窎窏窐窓窔窙窚窛窞窡窢贰发罚筏伐乏阀法珐藩帆番翻樊矾钒繁凡烦反返范贩犯饭泛坊芳方肪房防妨仿访纺放菲非啡飞肥匪诽吠肺废沸费芬酚吩氛分纷坟焚汾粉奋份忿愤粪丰封枫蜂峰锋风疯烽逢冯缝讽奉凤佛否夫敷肤孵扶拂辐幅氟符伏俘服"],["b840","窣窤窧窩窪窫窮",4,"窴",10,"竀",10,"竌",9,"竗竘竚竛竜竝竡竢竤竧",5,"竮竰竱竲竳"],["b880","竴",4,"竻竼竾笀笁笂笅笇笉笌笍笎笐笒笓笖笗笘笚笜笝笟笡笢笣笧笩笭浮涪福袱弗甫抚辅俯釜斧脯腑府腐赴副覆赋复傅付阜父腹负富讣附妇缚咐噶嘎该改概钙盖溉干甘杆柑竿肝赶感秆敢赣冈刚钢缸肛纲岗港杠篙皋高膏羔糕搞镐稿告哥歌搁戈鸽胳疙割革葛格蛤阁隔铬个各给根跟耕更庚羹"],["b940","笯笰笲笴笵笶笷笹笻笽笿",5,"筆筈筊筍筎筓筕筗筙筜筞筟筡筣",10,"筯筰筳筴筶筸筺筼筽筿箁箂箃箄箆",6,"箎箏"],["b980","箑箒箓箖箘箙箚箛箞箟箠箣箤箥箮箯箰箲箳箵箶箷箹",7,"篂篃範埂耿梗工攻功恭龚供躬公宫弓巩汞拱贡共钩勾沟苟狗垢构购够辜菇咕箍估沽孤姑鼓古蛊骨谷股故顾固雇刮瓜剐寡挂褂乖拐怪棺关官冠观管馆罐惯灌贯光广逛瑰规圭硅归龟闺轨鬼诡癸桂柜跪贵刽辊滚棍锅郭国果裹过哈"],["ba40","篅篈築篊篋篍篎篏篐篒篔",4,"篛篜篞篟篠篢篣篤篧篨篩篫篬篭篯篰篲",4,"篸篹篺篻篽篿",7,"簈簉簊簍簎簐",5,"簗簘簙"],["ba80","簚",4,"簠",5,"簨簩簫",12,"簹",5,"籂骸孩海氦亥害骇酣憨邯韩含涵寒函喊罕翰撼捍旱憾悍焊汗汉夯杭航壕嚎豪毫郝好耗号浩呵喝荷菏核禾和何合盒貉阂河涸赫褐鹤贺嘿黑痕很狠恨哼亨横衡恒轰哄烘虹鸿洪宏弘红喉侯猴吼厚候后呼乎忽瑚壶葫胡蝴狐糊湖"],["bb40","籃",9,"籎",36,"籵",5,"籾",9],["bb80","粈粊",6,"粓粔粖粙粚粛粠粡粣粦粧粨粩粫粬粭粯粰粴",4,"粺粻弧虎唬护互沪户花哗华猾滑画划化话槐徊怀淮坏欢环桓还缓换患唤痪豢焕涣宦幻荒慌黄磺蝗簧皇凰惶煌晃幌恍谎灰挥辉徽恢蛔回毁悔慧卉惠晦贿秽会烩汇讳诲绘荤昏婚魂浑混豁活伙火获或惑霍货祸击圾基机畸稽积箕"],["bc40","粿糀糂糃糄糆糉糋糎",6,"糘糚糛糝糞糡",6,"糩",5,"糰",7,"糹糺糼",13,"紋",5],["bc80","紑",14,"紡紣紤紥紦紨紩紪紬紭紮細",6,"肌饥迹激讥鸡姬绩缉吉极棘辑籍集及急疾汲即嫉级挤几脊己蓟技冀季伎祭剂悸济寄寂计记既忌际妓继纪嘉枷夹佳家加荚颊贾甲钾假稼价架驾嫁歼监坚尖笺间煎兼肩艰奸缄茧检柬碱硷拣捡简俭剪减荐槛鉴践贱见键箭件"],["bd40","紷",54,"絯",7],["bd80","絸",32,"健舰剑饯渐溅涧建僵姜将浆江疆蒋桨奖讲匠酱降蕉椒礁焦胶交郊浇骄娇嚼搅铰矫侥脚狡角饺缴绞剿教酵轿较叫窖揭接皆秸街阶截劫节桔杰捷睫竭洁结解姐戒藉芥界借介疥诫届巾筋斤金今津襟紧锦仅谨进靳晋禁近烬浸"],["be40","継",12,"綧",6,"綯",42],["be80","線",32,"尽劲荆兢茎睛晶鲸京惊精粳经井警景颈静境敬镜径痉靖竟竞净炯窘揪究纠玖韭久灸九酒厩救旧臼舅咎就疚鞠拘狙疽居驹菊局咀矩举沮聚拒据巨具距踞锯俱句惧炬剧捐鹃娟倦眷卷绢撅攫抉掘倔爵觉决诀绝均菌钧军君峻"],["bf40","緻",62],["bf80","縺縼",4,"繂",4,"繈",21,"俊竣浚郡骏喀咖卡咯开揩楷凯慨刊堪勘坎砍看康慷糠扛抗亢炕考拷烤靠坷苛柯棵磕颗科壳咳可渴克刻客课肯啃垦恳坑吭空恐孔控抠口扣寇枯哭窟苦酷库裤夸垮挎跨胯块筷侩快宽款匡筐狂框矿眶旷况亏盔岿窥葵奎魁傀"],["c040","繞",35,"纃",23,"纜纝纞"],["c080","纮纴纻纼绖绤绬绹缊缐缞缷缹缻",6,"罃罆",9,"罒罓馈愧溃坤昆捆困括扩廓阔垃拉喇蜡腊辣啦莱来赖蓝婪栏拦篮阑兰澜谰揽览懒缆烂滥琅榔狼廊郎朗浪捞劳牢老佬姥酪烙涝勒乐雷镭蕾磊累儡垒擂肋类泪棱楞冷厘梨犁黎篱狸离漓理李里鲤礼莉荔吏栗丽厉励砾历利傈例俐"],["c140","罖罙罛罜罝罞罠罣",4,"罫罬罭罯罰罳罵罶罷罸罺罻罼罽罿羀羂",7,"羋羍羏",4,"羕",4,"羛羜羠羢羣羥羦羨",6,"羱"],["c180","羳",4,"羺羻羾翀翂翃翄翆翇翈翉翋翍翏",4,"翖翗翙",5,"翢翣痢立粒沥隶力璃哩俩联莲连镰廉怜涟帘敛脸链恋炼练粮凉梁粱良两辆量晾亮谅撩聊僚疗燎寥辽潦了撂镣廖料列裂烈劣猎琳林磷霖临邻鳞淋凛赁吝拎玲菱零龄铃伶羚凌灵陵岭领另令溜琉榴硫馏留刘瘤流柳六龙聋咙笼窿"],["c240","翤翧翨翪翫翬翭翯翲翴",6,"翽翾翿耂耇耈耉耊耎耏耑耓耚耛耝耞耟耡耣耤耫",5,"耲耴耹耺耼耾聀聁聄聅聇聈聉聎聏聐聑聓聕聖聗"],["c280","聙聛",13,"聫",5,"聲",11,"隆垄拢陇楼娄搂篓漏陋芦卢颅庐炉掳卤虏鲁麓碌露路赂鹿潞禄录陆戮驴吕铝侣旅履屡缕虑氯律率滤绿峦挛孪滦卵乱掠略抡轮伦仑沦纶论萝螺罗逻锣箩骡裸落洛骆络妈麻玛码蚂马骂嘛吗埋买麦卖迈脉瞒馒蛮满蔓曼慢漫"],["c340","聾肁肂肅肈肊肍",5,"肔肕肗肙肞肣肦肧肨肬肰肳肵肶肸肹肻胅胇",4,"胏",6,"胘胟胠胢胣胦胮胵胷胹胻胾胿脀脁脃脄脅脇脈脋"],["c380","脌脕脗脙脛脜脝脟",12,"脭脮脰脳脴脵脷脹",4,"脿谩芒茫盲氓忙莽猫茅锚毛矛铆卯茂冒帽貌贸么玫枚梅酶霉煤没眉媒镁每美昧寐妹媚门闷们萌蒙檬盟锰猛梦孟眯醚靡糜迷谜弥米秘觅泌蜜密幂棉眠绵冕免勉娩缅面苗描瞄藐秒渺庙妙蔑灭民抿皿敏悯闽明螟鸣铭名命谬摸"],["c440","腀",5,"腇腉腍腎腏腒腖腗腘腛",4,"腡腢腣腤腦腨腪腫腬腯腲腳腵腶腷腸膁膃",4,"膉膋膌膍膎膐膒",5,"膙膚膞",4,"膤膥"],["c480","膧膩膫",7,"膴",5,"膼膽膾膿臄臅臇臈臉臋臍",6,"摹蘑模膜磨摩魔抹末莫墨默沫漠寞陌谋牟某拇牡亩姆母墓暮幕募慕木目睦牧穆拿哪呐钠那娜纳氖乃奶耐奈南男难囊挠脑恼闹淖呢馁内嫩能妮霓倪泥尼拟你匿腻逆溺蔫拈年碾撵捻念娘酿鸟尿捏聂孽啮镊镍涅您柠狞凝宁"],["c540","臔",14,"臤臥臦臨臩臫臮",4,"臵",5,"臽臿舃與",4,"舎舏舑舓舕",5,"舝舠舤舥舦舧舩舮舲舺舼舽舿"],["c580","艀艁艂艃艅艆艈艊艌艍艎艐",7,"艙艛艜艝艞艠",7,"艩拧泞牛扭钮纽脓浓农弄奴努怒女暖虐疟挪懦糯诺哦欧鸥殴藕呕偶沤啪趴爬帕怕琶拍排牌徘湃派攀潘盘磐盼畔判叛乓庞旁耪胖抛咆刨炮袍跑泡呸胚培裴赔陪配佩沛喷盆砰抨烹澎彭蓬棚硼篷膨朋鹏捧碰坯砒霹批披劈琵毗"],["c640","艪艫艬艭艱艵艶艷艸艻艼芀芁芃芅芆芇芉芌芐芓芔芕芖芚芛芞芠芢芣芧芲芵芶芺芻芼芿苀苂苃苅苆苉苐苖苙苚苝苢苧苨苩苪苬苭苮苰苲苳苵苶苸"],["c680","苺苼",4,"茊茋茍茐茒茓茖茘茙茝",9,"茩茪茮茰茲茷茻茽啤脾疲皮匹痞僻屁譬篇偏片骗飘漂瓢票撇瞥拼频贫品聘乒坪苹萍平凭瓶评屏坡泼颇婆破魄迫粕剖扑铺仆莆葡菩蒲埔朴圃普浦谱曝瀑期欺栖戚妻七凄漆柒沏其棋奇歧畦崎脐齐旗祈祁骑起岂乞企启契砌器气迄弃汽泣讫掐"],["c740","茾茿荁荂荄荅荈荊",4,"荓荕",4,"荝荢荰",6,"荹荺荾",6,"莇莈莊莋莌莍莏莐莑莔莕莖莗莙莚莝莟莡",6,"莬莭莮"],["c780","莯莵莻莾莿菂菃菄菆菈菉菋菍菎菐菑菒菓菕菗菙菚菛菞菢菣菤菦菧菨菫菬菭恰洽牵扦钎铅千迁签仟谦乾黔钱钳前潜遣浅谴堑嵌欠歉枪呛腔羌墙蔷强抢橇锹敲悄桥瞧乔侨巧鞘撬翘峭俏窍切茄且怯窃钦侵亲秦琴勤芹擒禽寝沁青轻氢倾卿清擎晴氰情顷请庆琼穷秋丘邱球求囚酋泅趋区蛆曲躯屈驱渠"],["c840","菮華菳",4,"菺菻菼菾菿萀萂萅萇萈萉萊萐萒",5,"萙萚萛萞",5,"萩",7,"萲",5,"萹萺萻萾",7,"葇葈葉"],["c880","葊",6,"葒",4,"葘葝葞葟葠葢葤",4,"葪葮葯葰葲葴葷葹葻葼取娶龋趣去圈颧权醛泉全痊拳犬券劝缺炔瘸却鹊榷确雀裙群然燃冉染瓤壤攘嚷让饶扰绕惹热壬仁人忍韧任认刃妊纫扔仍日戎茸蓉荣融熔溶容绒冗揉柔肉茹蠕儒孺如辱乳汝入褥软阮蕊瑞锐闰润若弱撒洒萨腮鳃塞赛三叁"],["c940","葽",4,"蒃蒄蒅蒆蒊蒍蒏",7,"蒘蒚蒛蒝蒞蒟蒠蒢",12,"蒰蒱蒳蒵蒶蒷蒻蒼蒾蓀蓂蓃蓅蓆蓇蓈蓋蓌蓎蓏蓒蓔蓕蓗"],["c980","蓘",4,"蓞蓡蓢蓤蓧",4,"蓭蓮蓯蓱",10,"蓽蓾蔀蔁蔂伞散桑嗓丧搔骚扫嫂瑟色涩森僧莎砂杀刹沙纱傻啥煞筛晒珊苫杉山删煽衫闪陕擅赡膳善汕扇缮墒伤商赏晌上尚裳梢捎稍烧芍勺韶少哨邵绍奢赊蛇舌舍赦摄射慑涉社设砷申呻伸身深娠绅神沈审婶甚肾慎渗声生甥牲升绳"],["ca40","蔃",8,"蔍蔎蔏蔐蔒蔔蔕蔖蔘蔙蔛蔜蔝蔞蔠蔢",8,"蔭",9,"蔾",4,"蕄蕅蕆蕇蕋",10],["ca80","蕗蕘蕚蕛蕜蕝蕟",4,"蕥蕦蕧蕩",8,"蕳蕵蕶蕷蕸蕼蕽蕿薀薁省盛剩胜圣师失狮施湿诗尸虱十石拾时什食蚀实识史矢使屎驶始式示士世柿事拭誓逝势是嗜噬适仕侍释饰氏市恃室视试收手首守寿授售受瘦兽蔬枢梳殊抒输叔舒淑疏书赎孰熟薯暑曙署蜀黍鼠属术述树束戍竖墅庶数漱"],["cb40","薂薃薆薈",6,"薐",10,"薝",6,"薥薦薧薩薫薬薭薱",5,"薸薺",6,"藂",6,"藊",4,"藑藒"],["cb80","藔藖",5,"藝",6,"藥藦藧藨藪",14,"恕刷耍摔衰甩帅栓拴霜双爽谁水睡税吮瞬顺舜说硕朔烁斯撕嘶思私司丝死肆寺嗣四伺似饲巳松耸怂颂送宋讼诵搜艘擞嗽苏酥俗素速粟僳塑溯宿诉肃酸蒜算虽隋随绥髓碎岁穗遂隧祟孙损笋蓑梭唆缩琐索锁所塌他它她塔"],["cc40","藹藺藼藽藾蘀",4,"蘆",10,"蘒蘓蘔蘕蘗",15,"蘨蘪",13,"蘹蘺蘻蘽蘾蘿虀"],["cc80","虁",11,"虒虓處",4,"虛虜虝號虠虡虣",7,"獭挞蹋踏胎苔抬台泰酞太态汰坍摊贪瘫滩坛檀痰潭谭谈坦毯袒碳探叹炭汤塘搪堂棠膛唐糖倘躺淌趟烫掏涛滔绦萄桃逃淘陶讨套特藤腾疼誊梯剔踢锑提题蹄啼体替嚏惕涕剃屉天添填田甜恬舔腆挑条迢眺跳贴铁帖厅听烃"],["cd40","虭虯虰虲",6,"蚃",6,"蚎",4,"蚔蚖",5,"蚞",4,"蚥蚦蚫蚭蚮蚲蚳蚷蚸蚹蚻",4,"蛁蛂蛃蛅蛈蛌蛍蛒蛓蛕蛖蛗蛚蛜"],["cd80","蛝蛠蛡蛢蛣蛥蛦蛧蛨蛪蛫蛬蛯蛵蛶蛷蛺蛻蛼蛽蛿蜁蜄蜅蜆蜋蜌蜎蜏蜐蜑蜔蜖汀廷停亭庭挺艇通桐酮瞳同铜彤童桶捅筒统痛偷投头透凸秃突图徒途涂屠土吐兔湍团推颓腿蜕褪退吞屯臀拖托脱鸵陀驮驼椭妥拓唾挖哇蛙洼娃瓦袜歪外豌弯湾玩顽丸烷完碗挽晚皖惋宛婉万腕汪王亡枉网往旺望忘妄威"],["ce40","蜙蜛蜝蜟蜠蜤蜦蜧蜨蜪蜫蜬蜭蜯蜰蜲蜳蜵蜶蜸蜹蜺蜼蜽蝀",6,"蝊蝋蝍蝏蝐蝑蝒蝔蝕蝖蝘蝚",5,"蝡蝢蝦",7,"蝯蝱蝲蝳蝵"],["ce80","蝷蝸蝹蝺蝿螀螁螄螆螇螉螊螌螎",4,"螔螕螖螘",6,"螠",4,"巍微危韦违桅围唯惟为潍维苇萎委伟伪尾纬未蔚味畏胃喂魏位渭谓尉慰卫瘟温蚊文闻纹吻稳紊问嗡翁瓮挝蜗涡窝我斡卧握沃巫呜钨乌污诬屋无芜梧吾吴毋武五捂午舞伍侮坞戊雾晤物勿务悟误昔熙析西硒矽晰嘻吸锡牺"],["cf40","螥螦螧螩螪螮螰螱螲螴螶螷螸螹螻螼螾螿蟁",4,"蟇蟈蟉蟌",4,"蟔",6,"蟜蟝蟞蟟蟡蟢蟣蟤蟦蟧蟨蟩蟫蟬蟭蟯",9],["cf80","蟺蟻蟼蟽蟿蠀蠁蠂蠄",5,"蠋",7,"蠔蠗蠘蠙蠚蠜",4,"蠣稀息希悉膝夕惜熄烯溪汐犀檄袭席习媳喜铣洗系隙戏细瞎虾匣霞辖暇峡侠狭下厦夏吓掀锨先仙鲜纤咸贤衔舷闲涎弦嫌显险现献县腺馅羡宪陷限线相厢镶香箱襄湘乡翔祥详想响享项巷橡像向象萧硝霄削哮嚣销消宵淆晓"],["d040","蠤",13,"蠳",5,"蠺蠻蠽蠾蠿衁衂衃衆",5,"衎",5,"衕衖衘衚",6,"衦衧衪衭衯衱衳衴衵衶衸衹衺"],["d080","衻衼袀袃袆袇袉袊袌袎袏袐袑袓袔袕袗",4,"袝",4,"袣袥",5,"小孝校肖啸笑效楔些歇蝎鞋协挟携邪斜胁谐写械卸蟹懈泄泻谢屑薪芯锌欣辛新忻心信衅星腥猩惺兴刑型形邢行醒幸杏性姓兄凶胸匈汹雄熊休修羞朽嗅锈秀袖绣墟戌需虚嘘须徐许蓄酗叙旭序畜恤絮婿绪续轩喧宣悬旋玄"],["d140","袬袮袯袰袲",4,"袸袹袺袻袽袾袿裀裃裄裇裈裊裋裌裍裏裐裑裓裖裗裚",4,"裠裡裦裧裩",6,"裲裵裶裷裺裻製裿褀褁褃",5],["d180","褉褋",4,"褑褔",4,"褜",4,"褢褣褤褦褧褨褩褬褭褮褯褱褲褳褵褷选癣眩绚靴薛学穴雪血勋熏循旬询寻驯巡殉汛训讯逊迅压押鸦鸭呀丫芽牙蚜崖衙涯雅哑亚讶焉咽阉烟淹盐严研蜒岩延言颜阎炎沿奄掩眼衍演艳堰燕厌砚雁唁彦焰宴谚验殃央鸯秧杨扬佯疡羊洋阳氧仰痒养样漾邀腰妖瑶"],["d240","褸",8,"襂襃襅",24,"襠",5,"襧",19,"襼"],["d280","襽襾覀覂覄覅覇",26,"摇尧遥窑谣姚咬舀药要耀椰噎耶爷野冶也页掖业叶曳腋夜液一壹医揖铱依伊衣颐夷遗移仪胰疑沂宜姨彝椅蚁倚已乙矣以艺抑易邑屹亿役臆逸肄疫亦裔意毅忆义益溢诣议谊译异翼翌绎茵荫因殷音阴姻吟银淫寅饮尹引隐"],["d340","覢",30,"觃觍觓觔觕觗觘觙觛觝觟觠觡觢觤觧觨觩觪觬觭觮觰觱觲觴",6],["d380","觻",4,"訁",5,"計",21,"印英樱婴鹰应缨莹萤营荧蝇迎赢盈影颖硬映哟拥佣臃痈庸雍踊蛹咏泳涌永恿勇用幽优悠忧尤由邮铀犹油游酉有友右佑釉诱又幼迂淤于盂榆虞愚舆余俞逾鱼愉渝渔隅予娱雨与屿禹宇语羽玉域芋郁吁遇喻峪御愈欲狱育誉"],["d440","訞",31,"訿",8,"詉",21],["d480","詟",25,"詺",6,"浴寓裕预豫驭鸳渊冤元垣袁原援辕园员圆猿源缘远苑愿怨院曰约越跃钥岳粤月悦阅耘云郧匀陨允运蕴酝晕韵孕匝砸杂栽哉灾宰载再在咱攒暂赞赃脏葬遭糟凿藻枣早澡蚤躁噪造皂灶燥责择则泽贼怎增憎曾赠扎喳渣札轧"],["d540","誁",7,"誋",7,"誔",46],["d580","諃",32,"铡闸眨栅榨咋乍炸诈摘斋宅窄债寨瞻毡詹粘沾盏斩辗崭展蘸栈占战站湛绽樟章彰漳张掌涨杖丈帐账仗胀瘴障招昭找沼赵照罩兆肇召遮折哲蛰辙者锗蔗这浙珍斟真甄砧臻贞针侦枕疹诊震振镇阵蒸挣睁征狰争怔整拯正政"],["d640","諤",34,"謈",27],["d680","謤謥謧",30,"帧症郑证芝枝支吱蜘知肢脂汁之织职直植殖执值侄址指止趾只旨纸志挚掷至致置帜峙制智秩稚质炙痔滞治窒中盅忠钟衷终种肿重仲众舟周州洲诌粥轴肘帚咒皱宙昼骤珠株蛛朱猪诸诛逐竹烛煮拄瞩嘱主著柱助蛀贮铸筑"],["d740","譆",31,"譧",4,"譭",25],["d780","讇",24,"讬讱讻诇诐诪谉谞住注祝驻抓爪拽专砖转撰赚篆桩庄装妆撞壮状椎锥追赘坠缀谆准捉拙卓桌琢茁酌啄着灼浊兹咨资姿滋淄孜紫仔籽滓子自渍字鬃棕踪宗综总纵邹走奏揍租足卒族祖诅阻组钻纂嘴醉最罪尊遵昨左佐柞做作坐座"],["d840","谸",8,"豂豃豄豅豈豊豋豍",7,"豖豗豘豙豛",5,"豣",6,"豬",6,"豴豵豶豷豻",6,"貃貄貆貇"],["d880","貈貋貍",6,"貕貖貗貙",20,"亍丌兀丐廿卅丕亘丞鬲孬噩丨禺丿匕乇夭爻卮氐囟胤馗毓睾鼗丶亟鼐乜乩亓芈孛啬嘏仄厍厝厣厥厮靥赝匚叵匦匮匾赜卦卣刂刈刎刭刳刿剀剌剞剡剜蒯剽劂劁劐劓冂罔亻仃仉仂仨仡仫仞伛仳伢佤仵伥伧伉伫佞佧攸佚佝"],["d940","貮",62],["d980","賭",32,"佟佗伲伽佶佴侑侉侃侏佾佻侪佼侬侔俦俨俪俅俚俣俜俑俟俸倩偌俳倬倏倮倭俾倜倌倥倨偾偃偕偈偎偬偻傥傧傩傺僖儆僭僬僦僮儇儋仝氽佘佥俎龠汆籴兮巽黉馘冁夔勹匍訇匐凫夙兕亠兖亳衮袤亵脔裒禀嬴蠃羸冫冱冽冼"],["da40","贎",14,"贠赑赒赗赟赥赨赩赪赬赮赯赱赲赸",8,"趂趃趆趇趈趉趌",4,"趒趓趕",9,"趠趡"],["da80","趢趤",12,"趲趶趷趹趻趽跀跁跂跅跇跈跉跊跍跐跒跓跔凇冖冢冥讠讦讧讪讴讵讷诂诃诋诏诎诒诓诔诖诘诙诜诟诠诤诨诩诮诰诳诶诹诼诿谀谂谄谇谌谏谑谒谔谕谖谙谛谘谝谟谠谡谥谧谪谫谮谯谲谳谵谶卩卺阝阢阡阱阪阽阼陂陉陔陟陧陬陲陴隈隍隗隰邗邛邝邙邬邡邴邳邶邺"],["db40","跕跘跙跜跠跡跢跥跦跧跩跭跮跰跱跲跴跶跼跾",6,"踆踇踈踋踍踎踐踑踒踓踕",7,"踠踡踤",4,"踫踭踰踲踳踴踶踷踸踻踼踾"],["db80","踿蹃蹅蹆蹌",4,"蹓",5,"蹚",11,"蹧蹨蹪蹫蹮蹱邸邰郏郅邾郐郄郇郓郦郢郜郗郛郫郯郾鄄鄢鄞鄣鄱鄯鄹酃酆刍奂劢劬劭劾哿勐勖勰叟燮矍廴凵凼鬯厶弁畚巯坌垩垡塾墼壅壑圩圬圪圳圹圮圯坜圻坂坩垅坫垆坼坻坨坭坶坳垭垤垌垲埏垧垴垓垠埕埘埚埙埒垸埴埯埸埤埝"],["dc40","蹳蹵蹷",4,"蹽蹾躀躂躃躄躆躈",6,"躑躒躓躕",6,"躝躟",11,"躭躮躰躱躳",6,"躻",7],["dc80","軃",10,"軏",21,"堋堍埽埭堀堞堙塄堠塥塬墁墉墚墀馨鼙懿艹艽艿芏芊芨芄芎芑芗芙芫芸芾芰苈苊苣芘芷芮苋苌苁芩芴芡芪芟苄苎芤苡茉苷苤茏茇苜苴苒苘茌苻苓茑茚茆茔茕苠苕茜荑荛荜茈莒茼茴茱莛荞茯荏荇荃荟荀茗荠茭茺茳荦荥"],["dd40","軥",62],["dd80","輤",32,"荨茛荩荬荪荭荮莰荸莳莴莠莪莓莜莅荼莶莩荽莸荻莘莞莨莺莼菁萁菥菘堇萘萋菝菽菖萜萸萑萆菔菟萏萃菸菹菪菅菀萦菰菡葜葑葚葙葳蒇蒈葺蒉葸萼葆葩葶蒌蒎萱葭蓁蓍蓐蓦蒽蓓蓊蒿蒺蓠蒡蒹蒴蒗蓥蓣蔌甍蔸蓰蔹蔟蔺"],["de40","轅",32,"轪辀辌辒辝辠辡辢辤辥辦辧辪辬辭辮辯農辳辴辵辷辸辺辻込辿迀迃迆"],["de80","迉",4,"迏迒迖迗迚迠迡迣迧迬迯迱迲迴迵迶迺迻迼迾迿逇逈逌逎逓逕逘蕖蔻蓿蓼蕙蕈蕨蕤蕞蕺瞢蕃蕲蕻薤薨薇薏蕹薮薜薅薹薷薰藓藁藜藿蘧蘅蘩蘖蘼廾弈夼奁耷奕奚奘匏尢尥尬尴扌扪抟抻拊拚拗拮挢拶挹捋捃掭揶捱捺掎掴捭掬掊捩掮掼揲揸揠揿揄揞揎摒揆掾摅摁搋搛搠搌搦搡摞撄摭撖"],["df40","這逜連逤逥逧",5,"逰",4,"逷逹逺逽逿遀遃遅遆遈",4,"過達違遖遙遚遜",5,"遤遦遧適遪遫遬遯",4,"遶",6,"遾邁"],["df80","還邅邆邇邉邊邌",4,"邒邔邖邘邚邜邞邟邠邤邥邧邨邩邫邭邲邷邼邽邿郀摺撷撸撙撺擀擐擗擤擢攉攥攮弋忒甙弑卟叱叽叩叨叻吒吖吆呋呒呓呔呖呃吡呗呙吣吲咂咔呷呱呤咚咛咄呶呦咝哐咭哂咴哒咧咦哓哔呲咣哕咻咿哌哙哚哜咩咪咤哝哏哞唛哧唠哽唔哳唢唣唏唑唧唪啧喏喵啉啭啁啕唿啐唼"],["e040","郂郃郆郈郉郋郌郍郒郔郕郖郘郙郚郞郟郠郣郤郥郩郪郬郮郰郱郲郳郵郶郷郹郺郻郼郿鄀鄁鄃鄅",19,"鄚鄛鄜"],["e080","鄝鄟鄠鄡鄤",10,"鄰鄲",6,"鄺",8,"酄唷啖啵啶啷唳唰啜喋嗒喃喱喹喈喁喟啾嗖喑啻嗟喽喾喔喙嗪嗷嗉嘟嗑嗫嗬嗔嗦嗝嗄嗯嗥嗲嗳嗌嗍嗨嗵嗤辔嘞嘈嘌嘁嘤嘣嗾嘀嘧嘭噘嘹噗嘬噍噢噙噜噌噔嚆噤噱噫噻噼嚅嚓嚯囔囗囝囡囵囫囹囿圄圊圉圜帏帙帔帑帱帻帼"],["e140","酅酇酈酑酓酔酕酖酘酙酛酜酟酠酦酧酨酫酭酳酺酻酼醀",4,"醆醈醊醎醏醓",6,"醜",5,"醤",5,"醫醬醰醱醲醳醶醷醸醹醻"],["e180","醼",10,"釈釋釐釒",9,"針",8,"帷幄幔幛幞幡岌屺岍岐岖岈岘岙岑岚岜岵岢岽岬岫岱岣峁岷峄峒峤峋峥崂崃崧崦崮崤崞崆崛嵘崾崴崽嵬嵛嵯嵝嵫嵋嵊嵩嵴嶂嶙嶝豳嶷巅彳彷徂徇徉後徕徙徜徨徭徵徼衢彡犭犰犴犷犸狃狁狎狍狒狨狯狩狲狴狷猁狳猃狺"],["e240","釦",62],["e280","鈥",32,"狻猗猓猡猊猞猝猕猢猹猥猬猸猱獐獍獗獠獬獯獾舛夥飧夤夂饣饧",5,"饴饷饽馀馄馇馊馍馐馑馓馔馕庀庑庋庖庥庠庹庵庾庳赓廒廑廛廨廪膺忄忉忖忏怃忮怄忡忤忾怅怆忪忭忸怙怵怦怛怏怍怩怫怊怿怡恸恹恻恺恂"],["e340","鉆",45,"鉵",16],["e380","銆",7,"銏",24,"恪恽悖悚悭悝悃悒悌悛惬悻悱惝惘惆惚悴愠愦愕愣惴愀愎愫慊慵憬憔憧憷懔懵忝隳闩闫闱闳闵闶闼闾阃阄阆阈阊阋阌阍阏阒阕阖阗阙阚丬爿戕氵汔汜汊沣沅沐沔沌汨汩汴汶沆沩泐泔沭泷泸泱泗沲泠泖泺泫泮沱泓泯泾"],["e440","銨",5,"銯",24,"鋉",31],["e480","鋩",32,"洹洧洌浃浈洇洄洙洎洫浍洮洵洚浏浒浔洳涑浯涞涠浞涓涔浜浠浼浣渚淇淅淞渎涿淠渑淦淝淙渖涫渌涮渫湮湎湫溲湟溆湓湔渲渥湄滟溱溘滠漭滢溥溧溽溻溷滗溴滏溏滂溟潢潆潇漤漕滹漯漶潋潴漪漉漩澉澍澌潸潲潼潺濑"],["e540","錊",51,"錿",10],["e580","鍊",31,"鍫濉澧澹澶濂濡濮濞濠濯瀚瀣瀛瀹瀵灏灞宀宄宕宓宥宸甯骞搴寤寮褰寰蹇謇辶迓迕迥迮迤迩迦迳迨逅逄逋逦逑逍逖逡逵逶逭逯遄遑遒遐遨遘遢遛暹遴遽邂邈邃邋彐彗彖彘尻咫屐屙孱屣屦羼弪弩弭艴弼鬻屮妁妃妍妩妪妣"],["e640","鍬",34,"鎐",27],["e680","鎬",29,"鏋鏌鏍妗姊妫妞妤姒妲妯姗妾娅娆姝娈姣姘姹娌娉娲娴娑娣娓婀婧婊婕娼婢婵胬媪媛婷婺媾嫫媲嫒嫔媸嫠嫣嫱嫖嫦嫘嫜嬉嬗嬖嬲嬷孀尕尜孚孥孳孑孓孢驵驷驸驺驿驽骀骁骅骈骊骐骒骓骖骘骛骜骝骟骠骢骣骥骧纟纡纣纥纨纩"],["e740","鏎",7,"鏗",54],["e780","鐎",32,"纭纰纾绀绁绂绉绋绌绐绔绗绛绠绡绨绫绮绯绱绲缍绶绺绻绾缁缂缃缇缈缋缌缏缑缒缗缙缜缛缟缡",6,"缪缫缬缭缯",4,"缵幺畿巛甾邕玎玑玮玢玟珏珂珑玷玳珀珉珈珥珙顼琊珩珧珞玺珲琏琪瑛琦琥琨琰琮琬"],["e840","鐯",14,"鐿",43,"鑬鑭鑮鑯"],["e880","鑰",20,"钑钖钘铇铏铓铔铚铦铻锜锠琛琚瑁瑜瑗瑕瑙瑷瑭瑾璜璎璀璁璇璋璞璨璩璐璧瓒璺韪韫韬杌杓杞杈杩枥枇杪杳枘枧杵枨枞枭枋杷杼柰栉柘栊柩枰栌柙枵柚枳柝栀柃枸柢栎柁柽栲栳桠桡桎桢桄桤梃栝桕桦桁桧桀栾桊桉栩梵梏桴桷梓桫棂楮棼椟椠棹"],["e940","锧锳锽镃镈镋镕镚镠镮镴镵長",7,"門",42],["e980","閫",32,"椤棰椋椁楗棣椐楱椹楠楂楝榄楫榀榘楸椴槌榇榈槎榉楦楣楹榛榧榻榫榭槔榱槁槊槟榕槠榍槿樯槭樗樘橥槲橄樾檠橐橛樵檎橹樽樨橘橼檑檐檩檗檫猷獒殁殂殇殄殒殓殍殚殛殡殪轫轭轱轲轳轵轶轸轷轹轺轼轾辁辂辄辇辋"],["ea40","闌",27,"闬闿阇阓阘阛阞阠阣",6,"阫阬阭阯阰阷阸阹阺阾陁陃陊陎陏陑陒陓陖陗"],["ea80","陘陙陚陜陝陞陠陣陥陦陫陭",4,"陳陸",12,"隇隉隊辍辎辏辘辚軎戋戗戛戟戢戡戥戤戬臧瓯瓴瓿甏甑甓攴旮旯旰昊昙杲昃昕昀炅曷昝昴昱昶昵耆晟晔晁晏晖晡晗晷暄暌暧暝暾曛曜曦曩贲贳贶贻贽赀赅赆赈赉赇赍赕赙觇觊觋觌觎觏觐觑牮犟牝牦牯牾牿犄犋犍犏犒挈挲掰"],["eb40","隌階隑隒隓隕隖隚際隝",9,"隨",7,"隱隲隴隵隷隸隺隻隿雂雃雈雊雋雐雑雓雔雖",9,"雡",6,"雫"],["eb80","雬雭雮雰雱雲雴雵雸雺電雼雽雿霂霃霅霊霋霌霐霑霒霔霕霗",4,"霝霟霠搿擘耄毪毳毽毵毹氅氇氆氍氕氘氙氚氡氩氤氪氲攵敕敫牍牒牖爰虢刖肟肜肓肼朊肽肱肫肭肴肷胧胨胩胪胛胂胄胙胍胗朐胝胫胱胴胭脍脎胲胼朕脒豚脶脞脬脘脲腈腌腓腴腙腚腱腠腩腼腽腭腧塍媵膈膂膑滕膣膪臌朦臊膻"],["ec40","霡",8,"霫霬霮霯霱霳",4,"霺霻霼霽霿",18,"靔靕靗靘靚靜靝靟靣靤靦靧靨靪",7],["ec80","靲靵靷",4,"靽",7,"鞆",4,"鞌鞎鞏鞐鞓鞕鞖鞗鞙",4,"臁膦欤欷欹歃歆歙飑飒飓飕飙飚殳彀毂觳斐齑斓於旆旄旃旌旎旒旖炀炜炖炝炻烀炷炫炱烨烊焐焓焖焯焱煳煜煨煅煲煊煸煺熘熳熵熨熠燠燔燧燹爝爨灬焘煦熹戾戽扃扈扉礻祀祆祉祛祜祓祚祢祗祠祯祧祺禅禊禚禧禳忑忐"],["ed40","鞞鞟鞡鞢鞤",6,"鞬鞮鞰鞱鞳鞵",46],["ed80","韤韥韨韮",4,"韴韷",23,"怼恝恚恧恁恙恣悫愆愍慝憩憝懋懑戆肀聿沓泶淼矶矸砀砉砗砘砑斫砭砜砝砹砺砻砟砼砥砬砣砩硎硭硖硗砦硐硇硌硪碛碓碚碇碜碡碣碲碹碥磔磙磉磬磲礅磴礓礤礞礴龛黹黻黼盱眄眍盹眇眈眚眢眙眭眦眵眸睐睑睇睃睚睨"],["ee40","頏",62],["ee80","顎",32,"睢睥睿瞍睽瞀瞌瞑瞟瞠瞰瞵瞽町畀畎畋畈畛畲畹疃罘罡罟詈罨罴罱罹羁罾盍盥蠲钅钆钇钋钊钌钍钏钐钔钗钕钚钛钜钣钤钫钪钭钬钯钰钲钴钶",4,"钼钽钿铄铈",6,"铐铑铒铕铖铗铙铘铛铞铟铠铢铤铥铧铨铪"],["ef40","顯",5,"颋颎颒颕颙颣風",37,"飏飐飔飖飗飛飜飝飠",4],["ef80","飥飦飩",30,"铩铫铮铯铳铴铵铷铹铼铽铿锃锂锆锇锉锊锍锎锏锒",4,"锘锛锝锞锟锢锪锫锩锬锱锲锴锶锷锸锼锾锿镂锵镄镅镆镉镌镎镏镒镓镔镖镗镘镙镛镞镟镝镡镢镤",8,"镯镱镲镳锺矧矬雉秕秭秣秫稆嵇稃稂稞稔"],["f040","餈",4,"餎餏餑",28,"餯",26],["f080","饊",9,"饖",12,"饤饦饳饸饹饻饾馂馃馉稹稷穑黏馥穰皈皎皓皙皤瓞瓠甬鸠鸢鸨",4,"鸲鸱鸶鸸鸷鸹鸺鸾鹁鹂鹄鹆鹇鹈鹉鹋鹌鹎鹑鹕鹗鹚鹛鹜鹞鹣鹦",6,"鹱鹭鹳疒疔疖疠疝疬疣疳疴疸痄疱疰痃痂痖痍痣痨痦痤痫痧瘃痱痼痿瘐瘀瘅瘌瘗瘊瘥瘘瘕瘙"],["f140","馌馎馚",10,"馦馧馩",47],["f180","駙",32,"瘛瘼瘢瘠癀瘭瘰瘿瘵癃瘾瘳癍癞癔癜癖癫癯翊竦穸穹窀窆窈窕窦窠窬窨窭窳衤衩衲衽衿袂袢裆袷袼裉裢裎裣裥裱褚裼裨裾裰褡褙褓褛褊褴褫褶襁襦襻疋胥皲皴矜耒耔耖耜耠耢耥耦耧耩耨耱耋耵聃聆聍聒聩聱覃顸颀颃"],["f240","駺",62],["f280","騹",32,"颉颌颍颏颔颚颛颞颟颡颢颥颦虍虔虬虮虿虺虼虻蚨蚍蚋蚬蚝蚧蚣蚪蚓蚩蚶蛄蚵蛎蚰蚺蚱蚯蛉蛏蚴蛩蛱蛲蛭蛳蛐蜓蛞蛴蛟蛘蛑蜃蜇蛸蜈蜊蜍蜉蜣蜻蜞蜥蜮蜚蜾蝈蜴蜱蜩蜷蜿螂蜢蝽蝾蝻蝠蝰蝌蝮螋蝓蝣蝼蝤蝙蝥螓螯螨蟒"],["f340","驚",17,"驲骃骉骍骎骔骕骙骦骩",6,"骲骳骴骵骹骻骽骾骿髃髄髆",4,"髍髎髏髐髒體髕髖髗髙髚髛髜"],["f380","髝髞髠髢髣髤髥髧髨髩髪髬髮髰",8,"髺髼",6,"鬄鬅鬆蟆螈螅螭螗螃螫蟥螬螵螳蟋蟓螽蟑蟀蟊蟛蟪蟠蟮蠖蠓蟾蠊蠛蠡蠹蠼缶罂罄罅舐竺竽笈笃笄笕笊笫笏筇笸笪笙笮笱笠笥笤笳笾笞筘筚筅筵筌筝筠筮筻筢筲筱箐箦箧箸箬箝箨箅箪箜箢箫箴篑篁篌篝篚篥篦篪簌篾篼簏簖簋"],["f440","鬇鬉",5,"鬐鬑鬒鬔",10,"鬠鬡鬢鬤",10,"鬰鬱鬳",7,"鬽鬾鬿魀魆魊魋魌魎魐魒魓魕",5],["f480","魛",32,"簟簪簦簸籁籀臾舁舂舄臬衄舡舢舣舭舯舨舫舸舻舳舴舾艄艉艋艏艚艟艨衾袅袈裘裟襞羝羟羧羯羰羲籼敉粑粝粜粞粢粲粼粽糁糇糌糍糈糅糗糨艮暨羿翎翕翥翡翦翩翮翳糸絷綦綮繇纛麸麴赳趄趔趑趱赧赭豇豉酊酐酎酏酤"],["f540","魼",62],["f580","鮻",32,"酢酡酰酩酯酽酾酲酴酹醌醅醐醍醑醢醣醪醭醮醯醵醴醺豕鹾趸跫踅蹙蹩趵趿趼趺跄跖跗跚跞跎跏跛跆跬跷跸跣跹跻跤踉跽踔踝踟踬踮踣踯踺蹀踹踵踽踱蹉蹁蹂蹑蹒蹊蹰蹶蹼蹯蹴躅躏躔躐躜躞豸貂貊貅貘貔斛觖觞觚觜"],["f640","鯜",62],["f680","鰛",32,"觥觫觯訾謦靓雩雳雯霆霁霈霏霎霪霭霰霾龀龃龅",5,"龌黾鼋鼍隹隼隽雎雒瞿雠銎銮鋈錾鍪鏊鎏鐾鑫鱿鲂鲅鲆鲇鲈稣鲋鲎鲐鲑鲒鲔鲕鲚鲛鲞",5,"鲥",4,"鲫鲭鲮鲰",7,"鲺鲻鲼鲽鳄鳅鳆鳇鳊鳋"],["f740","鰼",62],["f780","鱻鱽鱾鲀鲃鲄鲉鲊鲌鲏鲓鲖鲗鲘鲙鲝鲪鲬鲯鲹鲾",4,"鳈鳉鳑鳒鳚鳛鳠鳡鳌",4,"鳓鳔鳕鳗鳘鳙鳜鳝鳟鳢靼鞅鞑鞒鞔鞯鞫鞣鞲鞴骱骰骷鹘骶骺骼髁髀髅髂髋髌髑魅魃魇魉魈魍魑飨餍餮饕饔髟髡髦髯髫髻髭髹鬈鬏鬓鬟鬣麽麾縻麂麇麈麋麒鏖麝麟黛黜黝黠黟黢黩黧黥黪黯鼢鼬鼯鼹鼷鼽鼾齄"],["f840","鳣",62],["f880","鴢",32],["f940","鵃",62],["f980","鶂",32],["fa40","鶣",62],["fa80","鷢",32],["fb40","鸃",27,"鸤鸧鸮鸰鸴鸻鸼鹀鹍鹐鹒鹓鹔鹖鹙鹝鹟鹠鹡鹢鹥鹮鹯鹲鹴",9,"麀"],["fb80","麁麃麄麅麆麉麊麌",5,"麔",8,"麞麠",5,"麧麨麩麪"],["fc40","麫",8,"麵麶麷麹麺麼麿",4,"黅黆黇黈黊黋黌黐黒黓黕黖黗黙黚點黡黣黤黦黨黫黬黭黮黰",8,"黺黽黿",6],["fc80","鼆",4,"鼌鼏鼑鼒鼔鼕鼖鼘鼚",5,"鼡鼣",8,"鼭鼮鼰鼱"],["fd40","鼲",4,"鼸鼺鼼鼿",4,"齅",10,"齒",38],["fd80","齹",5,"龁龂龍",11,"龜龝龞龡",4,"郎凉秊裏隣"],["fe40","兀嗀﨎﨏﨑﨓﨔礼﨟蘒﨡﨣﨤﨧﨨﨩"]]');

/***/ }),

/***/ 21166:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["0","\\u0000",127],["8141","갂갃갅갆갋",4,"갘갞갟갡갢갣갥",6,"갮갲갳갴"],["8161","갵갶갷갺갻갽갾갿걁",9,"걌걎",5,"걕"],["8181","걖걗걙걚걛걝",18,"걲걳걵걶걹걻",4,"겂겇겈겍겎겏겑겒겓겕",6,"겞겢",5,"겫겭겮겱",6,"겺겾겿곀곂곃곅곆곇곉곊곋곍",7,"곖곘",7,"곢곣곥곦곩곫곭곮곲곴곷",4,"곾곿괁괂괃괅괇",4,"괎괐괒괓"],["8241","괔괕괖괗괙괚괛괝괞괟괡",7,"괪괫괮",5],["8261","괶괷괹괺괻괽",6,"굆굈굊",5,"굑굒굓굕굖굗"],["8281","굙",7,"굢굤",7,"굮굯굱굲굷굸굹굺굾궀궃",4,"궊궋궍궎궏궑",10,"궞",5,"궥",17,"궸",7,"귂귃귅귆귇귉",6,"귒귔",7,"귝귞귟귡귢귣귥",18],["8341","귺귻귽귾긂",5,"긊긌긎",5,"긕",7],["8361","긝",18,"긲긳긵긶긹긻긼"],["8381","긽긾긿깂깄깇깈깉깋깏깑깒깓깕깗",4,"깞깢깣깤깦깧깪깫깭깮깯깱",6,"깺깾",5,"꺆",5,"꺍",46,"꺿껁껂껃껅",6,"껎껒",5,"껚껛껝",8],["8441","껦껧껩껪껬껮",5,"껵껶껷껹껺껻껽",8],["8461","꼆꼉꼊꼋꼌꼎꼏꼑",18],["8481","꼤",7,"꼮꼯꼱꼳꼵",6,"꼾꽀꽄꽅꽆꽇꽊",5,"꽑",10,"꽞",5,"꽦",18,"꽺",5,"꾁꾂꾃꾅꾆꾇꾉",6,"꾒꾓꾔꾖",5,"꾝",26,"꾺꾻꾽꾾"],["8541","꾿꿁",5,"꿊꿌꿏",4,"꿕",6,"꿝",4],["8561","꿢",5,"꿪",5,"꿲꿳꿵꿶꿷꿹",6,"뀂뀃"],["8581","뀅",6,"뀍뀎뀏뀑뀒뀓뀕",6,"뀞",9,"뀩",26,"끆끇끉끋끍끏끐끑끒끖끘끚끛끜끞",29,"끾끿낁낂낃낅",6,"낎낐낒",5,"낛낝낞낣낤"],["8641","낥낦낧낪낰낲낶낷낹낺낻낽",6,"냆냊",5,"냒"],["8661","냓냕냖냗냙",6,"냡냢냣냤냦",10],["8681","냱",22,"넊넍넎넏넑넔넕넖넗넚넞",4,"넦넧넩넪넫넭",6,"넶넺",5,"녂녃녅녆녇녉",6,"녒녓녖녗녙녚녛녝녞녟녡",22,"녺녻녽녾녿놁놃",4,"놊놌놎놏놐놑놕놖놗놙놚놛놝"],["8741","놞",9,"놩",15],["8761","놹",18,"뇍뇎뇏뇑뇒뇓뇕"],["8781","뇖",5,"뇞뇠",7,"뇪뇫뇭뇮뇯뇱",7,"뇺뇼뇾",5,"눆눇눉눊눍",6,"눖눘눚",5,"눡",18,"눵",6,"눽",26,"뉙뉚뉛뉝뉞뉟뉡",6,"뉪",4],["8841","뉯",4,"뉶",5,"뉽",6,"늆늇늈늊",4],["8861","늏늒늓늕늖늗늛",4,"늢늤늧늨늩늫늭늮늯늱늲늳늵늶늷"],["8881","늸",15,"닊닋닍닎닏닑닓",4,"닚닜닞닟닠닡닣닧닩닪닰닱닲닶닼닽닾댂댃댅댆댇댉",6,"댒댖",5,"댝",54,"덗덙덚덝덠덡덢덣"],["8941","덦덨덪덬덭덯덲덳덵덶덷덹",6,"뎂뎆",5,"뎍"],["8961","뎎뎏뎑뎒뎓뎕",10,"뎢",5,"뎩뎪뎫뎭"],["8981","뎮",21,"돆돇돉돊돍돏돑돒돓돖돘돚돜돞돟돡돢돣돥돦돧돩",18,"돽",18,"됑",6,"됙됚됛됝됞됟됡",6,"됪됬",7,"됵",15],["8a41","둅",10,"둒둓둕둖둗둙",6,"둢둤둦"],["8a61","둧",4,"둭",18,"뒁뒂"],["8a81","뒃",4,"뒉",19,"뒞",5,"뒥뒦뒧뒩뒪뒫뒭",7,"뒶뒸뒺",5,"듁듂듃듅듆듇듉",6,"듑듒듓듔듖",5,"듞듟듡듢듥듧",4,"듮듰듲",5,"듹",26,"딖딗딙딚딝"],["8b41","딞",5,"딦딫",4,"딲딳딵딶딷딹",6,"땂땆"],["8b61","땇땈땉땊땎땏땑땒땓땕",6,"땞땢",8],["8b81","땫",52,"떢떣떥떦떧떩떬떭떮떯떲떶",4,"떾떿뗁뗂뗃뗅",6,"뗎뗒",5,"뗙",18,"뗭",18],["8c41","똀",15,"똒똓똕똖똗똙",4],["8c61","똞",6,"똦",5,"똭",6,"똵",5],["8c81","똻",12,"뙉",26,"뙥뙦뙧뙩",50,"뚞뚟뚡뚢뚣뚥",5,"뚭뚮뚯뚰뚲",16],["8d41","뛃",16,"뛕",8],["8d61","뛞",17,"뛱뛲뛳뛵뛶뛷뛹뛺"],["8d81","뛻",4,"뜂뜃뜄뜆",33,"뜪뜫뜭뜮뜱",6,"뜺뜼",7,"띅띆띇띉띊띋띍",6,"띖",9,"띡띢띣띥띦띧띩",6,"띲띴띶",5,"띾띿랁랂랃랅",6,"랎랓랔랕랚랛랝랞"],["8e41","랟랡",6,"랪랮",5,"랶랷랹",8],["8e61","럂",4,"럈럊",19],["8e81","럞",13,"럮럯럱럲럳럵",6,"럾렂",4,"렊렋렍렎렏렑",6,"렚렜렞",5,"렦렧렩렪렫렭",6,"렶렺",5,"롁롂롃롅",11,"롒롔",7,"롞롟롡롢롣롥",6,"롮롰롲",5,"롹롺롻롽",7],["8f41","뢅",7,"뢎",17],["8f61","뢠",7,"뢩",6,"뢱뢲뢳뢵뢶뢷뢹",4],["8f81","뢾뢿룂룄룆",5,"룍룎룏룑룒룓룕",7,"룞룠룢",5,"룪룫룭룮룯룱",6,"룺룼룾",5,"뤅",18,"뤙",6,"뤡",26,"뤾뤿륁륂륃륅",6,"륍륎륐륒",5],["9041","륚륛륝륞륟륡",6,"륪륬륮",5,"륶륷륹륺륻륽"],["9061","륾",5,"릆릈릋릌릏",15],["9081","릟",12,"릮릯릱릲릳릵",6,"릾맀맂",5,"맊맋맍맓",4,"맚맜맟맠맢맦맧맩맪맫맭",6,"맶맻",4,"먂",5,"먉",11,"먖",33,"먺먻먽먾먿멁멃멄멅멆"],["9141","멇멊멌멏멐멑멒멖멗멙멚멛멝",6,"멦멪",5],["9161","멲멳멵멶멷멹",9,"몆몈몉몊몋몍",5],["9181","몓",20,"몪몭몮몯몱몳",4,"몺몼몾",5,"뫅뫆뫇뫉",14,"뫚",33,"뫽뫾뫿묁묂묃묅",7,"묎묐묒",5,"묙묚묛묝묞묟묡",6],["9241","묨묪묬",7,"묷묹묺묿",4,"뭆뭈뭊뭋뭌뭎뭑뭒"],["9261","뭓뭕뭖뭗뭙",7,"뭢뭤",7,"뭭",4],["9281","뭲",21,"뮉뮊뮋뮍뮎뮏뮑",18,"뮥뮦뮧뮩뮪뮫뮭",6,"뮵뮶뮸",7,"믁믂믃믅믆믇믉",6,"믑믒믔",35,"믺믻믽믾밁"],["9341","밃",4,"밊밎밐밒밓밙밚밠밡밢밣밦밨밪밫밬밮밯밲밳밵"],["9361","밶밷밹",6,"뱂뱆뱇뱈뱊뱋뱎뱏뱑",8],["9381","뱚뱛뱜뱞",37,"벆벇벉벊벍벏",4,"벖벘벛",4,"벢벣벥벦벩",6,"벲벶",5,"벾벿볁볂볃볅",7,"볎볒볓볔볖볗볙볚볛볝",22,"볷볹볺볻볽"],["9441","볾",5,"봆봈봊",5,"봑봒봓봕",8],["9461","봞",5,"봥",6,"봭",12],["9481","봺",5,"뵁",6,"뵊뵋뵍뵎뵏뵑",6,"뵚",9,"뵥뵦뵧뵩",22,"붂붃붅붆붋",4,"붒붔붖붗붘붛붝",6,"붥",10,"붱",6,"붹",24],["9541","뷒뷓뷖뷗뷙뷚뷛뷝",11,"뷪",5,"뷱"],["9561","뷲뷳뷵뷶뷷뷹",6,"븁븂븄븆",5,"븎븏븑븒븓"],["9581","븕",6,"븞븠",35,"빆빇빉빊빋빍빏",4,"빖빘빜빝빞빟빢빣빥빦빧빩빫",4,"빲빶",4,"빾빿뺁뺂뺃뺅",6,"뺎뺒",5,"뺚",13,"뺩",14],["9641","뺸",23,"뻒뻓"],["9661","뻕뻖뻙",6,"뻡뻢뻦",5,"뻭",8],["9681","뻶",10,"뼂",5,"뼊",13,"뼚뼞",33,"뽂뽃뽅뽆뽇뽉",6,"뽒뽓뽔뽖",44],["9741","뾃",16,"뾕",8],["9761","뾞",17,"뾱",7],["9781","뾹",11,"뿆",5,"뿎뿏뿑뿒뿓뿕",6,"뿝뿞뿠뿢",89,"쀽쀾쀿"],["9841","쁀",16,"쁒",5,"쁙쁚쁛"],["9861","쁝쁞쁟쁡",6,"쁪",15],["9881","쁺",21,"삒삓삕삖삗삙",6,"삢삤삦",5,"삮삱삲삷",4,"삾샂샃샄샆샇샊샋샍샎샏샑",6,"샚샞",5,"샦샧샩샪샫샭",6,"샶샸샺",5,"섁섂섃섅섆섇섉",6,"섑섒섓섔섖",5,"섡섢섥섨섩섪섫섮"],["9941","섲섳섴섵섷섺섻섽섾섿셁",6,"셊셎",5,"셖셗"],["9961","셙셚셛셝",6,"셦셪",5,"셱셲셳셵셶셷셹셺셻"],["9981","셼",8,"솆",5,"솏솑솒솓솕솗",4,"솞솠솢솣솤솦솧솪솫솭솮솯솱",11,"솾",5,"쇅쇆쇇쇉쇊쇋쇍",6,"쇕쇖쇙",6,"쇡쇢쇣쇥쇦쇧쇩",6,"쇲쇴",7,"쇾쇿숁숂숃숅",6,"숎숐숒",5,"숚숛숝숞숡숢숣"],["9a41","숤숥숦숧숪숬숮숰숳숵",16],["9a61","쉆쉇쉉",6,"쉒쉓쉕쉖쉗쉙",6,"쉡쉢쉣쉤쉦"],["9a81","쉧",4,"쉮쉯쉱쉲쉳쉵",6,"쉾슀슂",5,"슊",5,"슑",6,"슙슚슜슞",5,"슦슧슩슪슫슮",5,"슶슸슺",33,"싞싟싡싢싥",5,"싮싰싲싳싴싵싷싺싽싾싿쌁",6,"쌊쌋쌎쌏"],["9b41","쌐쌑쌒쌖쌗쌙쌚쌛쌝",6,"쌦쌧쌪",8],["9b61","쌳",17,"썆",7],["9b81","썎",25,"썪썫썭썮썯썱썳",4,"썺썻썾",5,"쎅쎆쎇쎉쎊쎋쎍",50,"쏁",22,"쏚"],["9c41","쏛쏝쏞쏡쏣",4,"쏪쏫쏬쏮",5,"쏶쏷쏹",5],["9c61","쏿",8,"쐉",6,"쐑",9],["9c81","쐛",8,"쐥",6,"쐭쐮쐯쐱쐲쐳쐵",6,"쐾",9,"쑉",26,"쑦쑧쑩쑪쑫쑭",6,"쑶쑷쑸쑺",5,"쒁",18,"쒕",6,"쒝",12],["9d41","쒪",13,"쒹쒺쒻쒽",8],["9d61","쓆",25],["9d81","쓠",8,"쓪",5,"쓲쓳쓵쓶쓷쓹쓻쓼쓽쓾씂",9,"씍씎씏씑씒씓씕",6,"씝",10,"씪씫씭씮씯씱",6,"씺씼씾",5,"앆앇앋앏앐앑앒앖앚앛앜앟앢앣앥앦앧앩",6,"앲앶",5,"앾앿얁얂얃얅얆얈얉얊얋얎얐얒얓얔"],["9e41","얖얙얚얛얝얞얟얡",7,"얪",9,"얶"],["9e61","얷얺얿",4,"엋엍엏엒엓엕엖엗엙",6,"엢엤엦엧"],["9e81","엨엩엪엫엯엱엲엳엵엸엹엺엻옂옃옄옉옊옋옍옎옏옑",6,"옚옝",6,"옦옧옩옪옫옯옱옲옶옸옺옼옽옾옿왂왃왅왆왇왉",6,"왒왖",5,"왞왟왡",10,"왭왮왰왲",5,"왺왻왽왾왿욁",6,"욊욌욎",5,"욖욗욙욚욛욝",6,"욦"],["9f41","욨욪",5,"욲욳욵욶욷욻",4,"웂웄웆",5,"웎"],["9f61","웏웑웒웓웕",6,"웞웟웢",5,"웪웫웭웮웯웱웲"],["9f81","웳",4,"웺웻웼웾",5,"윆윇윉윊윋윍",6,"윖윘윚",5,"윢윣윥윦윧윩",6,"윲윴윶윸윹윺윻윾윿읁읂읃읅",4,"읋읎읐읙읚읛읝읞읟읡",6,"읩읪읬",7,"읶읷읹읺읻읿잀잁잂잆잋잌잍잏잒잓잕잙잛",4,"잢잧",4,"잮잯잱잲잳잵잶잷"],["a041","잸잹잺잻잾쟂",5,"쟊쟋쟍쟏쟑",6,"쟙쟚쟛쟜"],["a061","쟞",5,"쟥쟦쟧쟩쟪쟫쟭",13],["a081","쟻",4,"젂젃젅젆젇젉젋",4,"젒젔젗",4,"젞젟젡젢젣젥",6,"젮젰젲",5,"젹젺젻젽젾젿졁",6,"졊졋졎",5,"졕",26,"졲졳졵졶졷졹졻",4,"좂좄좈좉좊좎",5,"좕",7,"좞좠좢좣좤"],["a141","좥좦좧좩",18,"좾좿죀죁"],["a161","죂죃죅죆죇죉죊죋죍",6,"죖죘죚",5,"죢죣죥"],["a181","죦",14,"죶",5,"죾죿줁줂줃줇",4,"줎　、。·‥…¨〃­―∥＼∼‘’“”〔〕〈",9,"±×÷≠≤≥∞∴°′″℃Å￠￡￥♂♀∠⊥⌒∂∇≡≒§※☆★○●◎◇◆□■△▲▽▼→←↑↓↔〓≪≫√∽∝∵∫∬∈∋⊆⊇⊂⊃∪∩∧∨￢"],["a241","줐줒",5,"줙",18],["a261","줭",6,"줵",18],["a281","쥈",7,"쥒쥓쥕쥖쥗쥙",6,"쥢쥤",7,"쥭쥮쥯⇒⇔∀∃´～ˇ˘˝˚˙¸˛¡¿ː∮∑∏¤℉‰◁◀▷▶♤♠♡♥♧♣⊙◈▣◐◑▒▤▥▨▧▦▩♨☏☎☜☞¶†‡↕↗↙↖↘♭♩♪♬㉿㈜№㏇™㏂㏘℡€®"],["a341","쥱쥲쥳쥵",6,"쥽",10,"즊즋즍즎즏"],["a361","즑",6,"즚즜즞",16],["a381","즯",16,"짂짃짅짆짉짋",4,"짒짔짗짘짛！",58,"￦］",32,"￣"],["a441","짞짟짡짣짥짦짨짩짪짫짮짲",5,"짺짻짽짾짿쨁쨂쨃쨄"],["a461","쨅쨆쨇쨊쨎",5,"쨕쨖쨗쨙",12],["a481","쨦쨧쨨쨪",28,"ㄱ",93],["a541","쩇",4,"쩎쩏쩑쩒쩓쩕",6,"쩞쩢",5,"쩩쩪"],["a561","쩫",17,"쩾",5,"쪅쪆"],["a581","쪇",16,"쪙",14,"ⅰ",9],["a5b0","Ⅰ",9],["a5c1","Α",16,"Σ",6],["a5e1","α",16,"σ",6],["a641","쪨",19,"쪾쪿쫁쫂쫃쫅"],["a661","쫆",5,"쫎쫐쫒쫔쫕쫖쫗쫚",5,"쫡",6],["a681","쫨쫩쫪쫫쫭",6,"쫵",18,"쬉쬊─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂┒┑┚┙┖┕┎┍┞┟┡┢┦┧┩┪┭┮┱┲┵┶┹┺┽┾╀╁╃",7],["a741","쬋",4,"쬑쬒쬓쬕쬖쬗쬙",6,"쬢",7],["a761","쬪",22,"쭂쭃쭄"],["a781","쭅쭆쭇쭊쭋쭍쭎쭏쭑",6,"쭚쭛쭜쭞",5,"쭥",7,"㎕㎖㎗ℓ㎘㏄㎣㎤㎥㎦㎙",9,"㏊㎍㎎㎏㏏㎈㎉㏈㎧㎨㎰",9,"㎀",4,"㎺",5,"㎐",4,"Ω㏀㏁㎊㎋㎌㏖㏅㎭㎮㎯㏛㎩㎪㎫㎬㏝㏐㏓㏃㏉㏜㏆"],["a841","쭭",10,"쭺",14],["a861","쮉",18,"쮝",6],["a881","쮤",19,"쮹",11,"ÆÐªĦ"],["a8a6","Ĳ"],["a8a8","ĿŁØŒºÞŦŊ"],["a8b1","㉠",27,"ⓐ",25,"①",14,"½⅓⅔¼¾⅛⅜⅝⅞"],["a941","쯅",14,"쯕",10],["a961","쯠쯡쯢쯣쯥쯦쯨쯪",18],["a981","쯽",14,"찎찏찑찒찓찕",6,"찞찟찠찣찤æđðħıĳĸŀłøœßþŧŋŉ㈀",27,"⒜",25,"⑴",14,"¹²³⁴ⁿ₁₂₃₄"],["aa41","찥찦찪찫찭찯찱",6,"찺찿",4,"챆챇챉챊챋챍챎"],["aa61","챏",4,"챖챚",5,"챡챢챣챥챧챩",6,"챱챲"],["aa81","챳챴챶",29,"ぁ",82],["ab41","첔첕첖첗첚첛첝첞첟첡",6,"첪첮",5,"첶첷첹"],["ab61","첺첻첽",6,"쳆쳈쳊",5,"쳑쳒쳓쳕",5],["ab81","쳛",8,"쳥",6,"쳭쳮쳯쳱",12,"ァ",85],["ac41","쳾쳿촀촂",5,"촊촋촍촎촏촑",6,"촚촜촞촟촠"],["ac61","촡촢촣촥촦촧촩촪촫촭",11,"촺",4],["ac81","촿",28,"쵝쵞쵟А",5,"ЁЖ",25],["acd1","а",5,"ёж",25],["ad41","쵡쵢쵣쵥",6,"쵮쵰쵲",5,"쵹",7],["ad61","춁",6,"춉",10,"춖춗춙춚춛춝춞춟"],["ad81","춠춡춢춣춦춨춪",5,"춱",18,"췅"],["ae41","췆",5,"췍췎췏췑",16],["ae61","췢",5,"췩췪췫췭췮췯췱",6,"췺췼췾",4],["ae81","츃츅츆츇츉츊츋츍",6,"츕츖츗츘츚",5,"츢츣츥츦츧츩츪츫"],["af41","츬츭츮츯츲츴츶",19],["af61","칊",13,"칚칛칝칞칢",5,"칪칬"],["af81","칮",5,"칶칷칹칺칻칽",6,"캆캈캊",5,"캒캓캕캖캗캙"],["b041","캚",5,"캢캦",5,"캮",12],["b061","캻",5,"컂",19],["b081","컖",13,"컦컧컩컪컭",6,"컶컺",5,"가각간갇갈갉갊감",7,"같",4,"갠갤갬갭갯갰갱갸갹갼걀걋걍걔걘걜거걱건걷걸걺검겁것겄겅겆겉겊겋게겐겔겜겝겟겠겡겨격겪견겯결겸겹겻겼경곁계곈곌곕곗고곡곤곧골곪곬곯곰곱곳공곶과곽관괄괆"],["b141","켂켃켅켆켇켉",6,"켒켔켖",5,"켝켞켟켡켢켣"],["b161","켥",6,"켮켲",5,"켹",11],["b181","콅",14,"콖콗콙콚콛콝",6,"콦콨콪콫콬괌괍괏광괘괜괠괩괬괭괴괵괸괼굄굅굇굉교굔굘굡굣구국군굳굴굵굶굻굼굽굿궁궂궈궉권궐궜궝궤궷귀귁귄귈귐귑귓규균귤그극근귿글긁금급긋긍긔기긱긴긷길긺김깁깃깅깆깊까깍깎깐깔깖깜깝깟깠깡깥깨깩깬깰깸"],["b241","콭콮콯콲콳콵콶콷콹",6,"쾁쾂쾃쾄쾆",5,"쾍"],["b261","쾎",18,"쾢",5,"쾩"],["b281","쾪",5,"쾱",18,"쿅",6,"깹깻깼깽꺄꺅꺌꺼꺽꺾껀껄껌껍껏껐껑께껙껜껨껫껭껴껸껼꼇꼈꼍꼐꼬꼭꼰꼲꼴꼼꼽꼿꽁꽂꽃꽈꽉꽐꽜꽝꽤꽥꽹꾀꾄꾈꾐꾑꾕꾜꾸꾹꾼꿀꿇꿈꿉꿋꿍꿎꿔꿜꿨꿩꿰꿱꿴꿸뀀뀁뀄뀌뀐뀔뀜뀝뀨끄끅끈끊끌끎끓끔끕끗끙"],["b341","쿌",19,"쿢쿣쿥쿦쿧쿩"],["b361","쿪",5,"쿲쿴쿶",5,"쿽쿾쿿퀁퀂퀃퀅",5],["b381","퀋",5,"퀒",5,"퀙",19,"끝끼끽낀낄낌낍낏낑나낙낚난낟날낡낢남납낫",4,"낱낳내낵낸낼냄냅냇냈냉냐냑냔냘냠냥너넉넋넌널넒넓넘넙넛넜넝넣네넥넨넬넴넵넷넸넹녀녁년녈념녑녔녕녘녜녠노녹논놀놂놈놉놋농높놓놔놘놜놨뇌뇐뇔뇜뇝"],["b441","퀮",5,"퀶퀷퀹퀺퀻퀽",6,"큆큈큊",5],["b461","큑큒큓큕큖큗큙",6,"큡",10,"큮큯"],["b481","큱큲큳큵",6,"큾큿킀킂",18,"뇟뇨뇩뇬뇰뇹뇻뇽누눅눈눋눌눔눕눗눙눠눴눼뉘뉜뉠뉨뉩뉴뉵뉼늄늅늉느늑는늘늙늚늠늡늣능늦늪늬늰늴니닉닌닐닒님닙닛닝닢다닥닦단닫",4,"닳담답닷",4,"닿대댁댄댈댐댑댓댔댕댜더덕덖던덛덜덞덟덤덥"],["b541","킕",14,"킦킧킩킪킫킭",5],["b561","킳킶킸킺",5,"탂탃탅탆탇탊",5,"탒탖",4],["b581","탛탞탟탡탢탣탥",6,"탮탲",5,"탹",11,"덧덩덫덮데덱덴델뎀뎁뎃뎄뎅뎌뎐뎔뎠뎡뎨뎬도독돈돋돌돎돐돔돕돗동돛돝돠돤돨돼됐되된될됨됩됫됴두둑둔둘둠둡둣둥둬뒀뒈뒝뒤뒨뒬뒵뒷뒹듀듄듈듐듕드득든듣들듦듬듭듯등듸디딕딘딛딜딤딥딧딨딩딪따딱딴딸"],["b641","턅",7,"턎",17],["b661","턠",15,"턲턳턵턶턷턹턻턼턽턾"],["b681","턿텂텆",5,"텎텏텑텒텓텕",6,"텞텠텢",5,"텩텪텫텭땀땁땃땄땅땋때땍땐땔땜땝땟땠땡떠떡떤떨떪떫떰떱떳떴떵떻떼떽뗀뗄뗌뗍뗏뗐뗑뗘뗬또똑똔똘똥똬똴뙈뙤뙨뚜뚝뚠뚤뚫뚬뚱뛔뛰뛴뛸뜀뜁뜅뜨뜩뜬뜯뜰뜸뜹뜻띄띈띌띔띕띠띤띨띰띱띳띵라락란랄람랍랏랐랑랒랖랗"],["b741","텮",13,"텽",6,"톅톆톇톉톊"],["b761","톋",20,"톢톣톥톦톧"],["b781","톩",6,"톲톴톶톷톸톹톻톽톾톿퇁",14,"래랙랜랠램랩랫랬랭랴략랸럇량러럭런럴럼럽럿렀렁렇레렉렌렐렘렙렛렝려력련렬렴렵렷렸령례롄롑롓로록론롤롬롭롯롱롸롼뢍뢨뢰뢴뢸룀룁룃룅료룐룔룝룟룡루룩룬룰룸룹룻룽뤄뤘뤠뤼뤽륀륄륌륏륑류륙륜률륨륩"],["b841","퇐",7,"퇙",17],["b861","퇫",8,"퇵퇶퇷퇹",13],["b881","툈툊",5,"툑",24,"륫륭르륵른를름릅릇릉릊릍릎리릭린릴림립릿링마막만많",4,"맘맙맛망맞맡맣매맥맨맬맴맵맷맸맹맺먀먁먈먕머먹먼멀멂멈멉멋멍멎멓메멕멘멜멤멥멧멨멩며멱면멸몃몄명몇몌모목몫몬몰몲몸몹못몽뫄뫈뫘뫙뫼"],["b941","툪툫툮툯툱툲툳툵",6,"툾퉀퉂",5,"퉉퉊퉋퉌"],["b961","퉍",14,"퉝",6,"퉥퉦퉧퉨"],["b981","퉩",22,"튂튃튅튆튇튉튊튋튌묀묄묍묏묑묘묜묠묩묫무묵묶문묻물묽묾뭄뭅뭇뭉뭍뭏뭐뭔뭘뭡뭣뭬뮈뮌뮐뮤뮨뮬뮴뮷므믄믈믐믓미믹민믿밀밂밈밉밋밌밍및밑바",4,"받",4,"밤밥밧방밭배백밴밸뱀뱁뱃뱄뱅뱉뱌뱍뱐뱝버벅번벋벌벎범법벗"],["ba41","튍튎튏튒튓튔튖",5,"튝튞튟튡튢튣튥",6,"튭"],["ba61","튮튯튰튲",5,"튺튻튽튾틁틃",4,"틊틌",5],["ba81","틒틓틕틖틗틙틚틛틝",6,"틦",9,"틲틳틵틶틷틹틺벙벚베벡벤벧벨벰벱벳벴벵벼벽변별볍볏볐병볕볘볜보복볶본볼봄봅봇봉봐봔봤봬뵀뵈뵉뵌뵐뵘뵙뵤뵨부북분붇불붉붊붐붑붓붕붙붚붜붤붰붸뷔뷕뷘뷜뷩뷰뷴뷸븀븃븅브븍븐블븜븝븟비빅빈빌빎빔빕빗빙빚빛빠빡빤"],["bb41","틻",4,"팂팄팆",5,"팏팑팒팓팕팗",4,"팞팢팣"],["bb61","팤팦팧팪팫팭팮팯팱",6,"팺팾",5,"퍆퍇퍈퍉"],["bb81","퍊",31,"빨빪빰빱빳빴빵빻빼빽뺀뺄뺌뺍뺏뺐뺑뺘뺙뺨뻐뻑뻔뻗뻘뻠뻣뻤뻥뻬뼁뼈뼉뼘뼙뼛뼜뼝뽀뽁뽄뽈뽐뽑뽕뾔뾰뿅뿌뿍뿐뿔뿜뿟뿡쀼쁑쁘쁜쁠쁨쁩삐삑삔삘삠삡삣삥사삭삯산삳살삵삶삼삽삿샀상샅새색샌샐샘샙샛샜생샤"],["bc41","퍪",17,"퍾퍿펁펂펃펅펆펇"],["bc61","펈펉펊펋펎펒",5,"펚펛펝펞펟펡",6,"펪펬펮"],["bc81","펯",4,"펵펶펷펹펺펻펽",6,"폆폇폊",5,"폑",5,"샥샨샬샴샵샷샹섀섄섈섐섕서",4,"섣설섦섧섬섭섯섰성섶세섹센셀셈셉셋셌셍셔셕션셜셤셥셧셨셩셰셴셸솅소속솎손솔솖솜솝솟송솥솨솩솬솰솽쇄쇈쇌쇔쇗쇘쇠쇤쇨쇰쇱쇳쇼쇽숀숄숌숍숏숑수숙순숟술숨숩숫숭"],["bd41","폗폙",7,"폢폤",7,"폮폯폱폲폳폵폶폷"],["bd61","폸폹폺폻폾퐀퐂",5,"퐉",13],["bd81","퐗",5,"퐞",25,"숯숱숲숴쉈쉐쉑쉔쉘쉠쉥쉬쉭쉰쉴쉼쉽쉿슁슈슉슐슘슛슝스슥슨슬슭슴습슷승시식신싣실싫심십싯싱싶싸싹싻싼쌀쌈쌉쌌쌍쌓쌔쌕쌘쌜쌤쌥쌨쌩썅써썩썬썰썲썸썹썼썽쎄쎈쎌쏀쏘쏙쏜쏟쏠쏢쏨쏩쏭쏴쏵쏸쐈쐐쐤쐬쐰"],["be41","퐸",7,"푁푂푃푅",14],["be61","푔",7,"푝푞푟푡푢푣푥",7,"푮푰푱푲"],["be81","푳",4,"푺푻푽푾풁풃",4,"풊풌풎",5,"풕",8,"쐴쐼쐽쑈쑤쑥쑨쑬쑴쑵쑹쒀쒔쒜쒸쒼쓩쓰쓱쓴쓸쓺쓿씀씁씌씐씔씜씨씩씬씰씸씹씻씽아악안앉않알앍앎앓암압앗았앙앝앞애액앤앨앰앱앳앴앵야약얀얄얇얌얍얏양얕얗얘얜얠얩어억언얹얻얼얽얾엄",6,"엌엎"],["bf41","풞",10,"풪",14],["bf61","풹",18,"퓍퓎퓏퓑퓒퓓퓕"],["bf81","퓖",5,"퓝퓞퓠",7,"퓩퓪퓫퓭퓮퓯퓱",6,"퓹퓺퓼에엑엔엘엠엡엣엥여역엮연열엶엷염",5,"옅옆옇예옌옐옘옙옛옜오옥온올옭옮옰옳옴옵옷옹옻와왁완왈왐왑왓왔왕왜왝왠왬왯왱외왹왼욀욈욉욋욍요욕욘욜욤욥욧용우욱운울욹욺움웁웃웅워웍원월웜웝웠웡웨"],["c041","퓾",5,"픅픆픇픉픊픋픍",6,"픖픘",5],["c061","픞",25],["c081","픸픹픺픻픾픿핁핂핃핅",6,"핎핐핒",5,"핚핛핝핞핟핡핢핣웩웬웰웸웹웽위윅윈윌윔윕윗윙유육윤율윰윱윳융윷으윽은을읊음읍읏응",7,"읜읠읨읫이익인일읽읾잃임입잇있잉잊잎자작잔잖잗잘잚잠잡잣잤장잦재잭잰잴잼잽잿쟀쟁쟈쟉쟌쟎쟐쟘쟝쟤쟨쟬저적전절젊"],["c141","핤핦핧핪핬핮",5,"핶핷핹핺핻핽",6,"햆햊햋"],["c161","햌햍햎햏햑",19,"햦햧"],["c181","햨",31,"점접젓정젖제젝젠젤젬젭젯젱져젼졀졈졉졌졍졔조족존졸졺좀좁좃종좆좇좋좌좍좔좝좟좡좨좼좽죄죈죌죔죕죗죙죠죡죤죵주죽준줄줅줆줌줍줏중줘줬줴쥐쥑쥔쥘쥠쥡쥣쥬쥰쥴쥼즈즉즌즐즘즙즛증지직진짇질짊짐집짓"],["c241","헊헋헍헎헏헑헓",4,"헚헜헞",5,"헦헧헩헪헫헭헮"],["c261","헯",4,"헶헸헺",5,"혂혃혅혆혇혉",6,"혒"],["c281","혖",5,"혝혞혟혡혢혣혥",7,"혮",9,"혺혻징짖짙짚짜짝짠짢짤짧짬짭짯짰짱째짹짼쨀쨈쨉쨋쨌쨍쨔쨘쨩쩌쩍쩐쩔쩜쩝쩟쩠쩡쩨쩽쪄쪘쪼쪽쫀쫄쫌쫍쫏쫑쫓쫘쫙쫠쫬쫴쬈쬐쬔쬘쬠쬡쭁쭈쭉쭌쭐쭘쭙쭝쭤쭸쭹쮜쮸쯔쯤쯧쯩찌찍찐찔찜찝찡찢찧차착찬찮찰참찹찻"],["c341","혽혾혿홁홂홃홄홆홇홊홌홎홏홐홒홓홖홗홙홚홛홝",4],["c361","홢",4,"홨홪",5,"홲홳홵",11],["c381","횁횂횄횆",5,"횎횏횑횒횓횕",7,"횞횠횢",5,"횩횪찼창찾채책챈챌챔챕챗챘챙챠챤챦챨챰챵처척천철첨첩첫첬청체첵첸첼쳄쳅쳇쳉쳐쳔쳤쳬쳰촁초촉촌촐촘촙촛총촤촨촬촹최쵠쵤쵬쵭쵯쵱쵸춈추축춘출춤춥춧충춰췄췌췐취췬췰췸췹췻췽츄츈츌츔츙츠측츤츨츰츱츳층"],["c441","횫횭횮횯횱",7,"횺횼",7,"훆훇훉훊훋"],["c461","훍훎훏훐훒훓훕훖훘훚",5,"훡훢훣훥훦훧훩",4],["c481","훮훯훱훲훳훴훶",5,"훾훿휁휂휃휅",11,"휒휓휔치칙친칟칠칡침칩칫칭카칵칸칼캄캅캇캉캐캑캔캘캠캡캣캤캥캬캭컁커컥컨컫컬컴컵컷컸컹케켁켄켈켐켑켓켕켜켠켤켬켭켯켰켱켸코콕콘콜콤콥콧콩콰콱콴콸쾀쾅쾌쾡쾨쾰쿄쿠쿡쿤쿨쿰쿱쿳쿵쿼퀀퀄퀑퀘퀭퀴퀵퀸퀼"],["c541","휕휖휗휚휛휝휞휟휡",6,"휪휬휮",5,"휶휷휹"],["c561","휺휻휽",6,"흅흆흈흊",5,"흒흓흕흚",4],["c581","흟흢흤흦흧흨흪흫흭흮흯흱흲흳흵",6,"흾흿힀힂",5,"힊힋큄큅큇큉큐큔큘큠크큭큰클큼큽킁키킥킨킬킴킵킷킹타탁탄탈탉탐탑탓탔탕태택탠탤탬탭탯탰탱탸턍터턱턴털턺텀텁텃텄텅테텍텐텔템텝텟텡텨텬텼톄톈토톡톤톨톰톱톳통톺톼퇀퇘퇴퇸툇툉툐투툭툰툴툼툽툿퉁퉈퉜"],["c641","힍힎힏힑",6,"힚힜힞",5],["c6a1","퉤튀튁튄튈튐튑튕튜튠튤튬튱트특튼튿틀틂틈틉틋틔틘틜틤틥티틱틴틸팀팁팃팅파팍팎판팔팖팜팝팟팠팡팥패팩팬팰팸팹팻팼팽퍄퍅퍼퍽펀펄펌펍펏펐펑페펙펜펠펨펩펫펭펴편펼폄폅폈평폐폘폡폣포폭폰폴폼폽폿퐁"],["c7a1","퐈퐝푀푄표푠푤푭푯푸푹푼푿풀풂품풉풋풍풔풩퓌퓐퓔퓜퓟퓨퓬퓰퓸퓻퓽프픈플픔픕픗피픽핀필핌핍핏핑하학한할핥함합핫항해핵핸핼햄햅햇했행햐향허헉헌헐헒험헙헛헝헤헥헨헬헴헵헷헹혀혁현혈혐협혓혔형혜혠"],["c8a1","혤혭호혹혼홀홅홈홉홋홍홑화확환활홧황홰홱홴횃횅회획횐횔횝횟횡효횬횰횹횻후훅훈훌훑훔훗훙훠훤훨훰훵훼훽휀휄휑휘휙휜휠휨휩휫휭휴휵휸휼흄흇흉흐흑흔흖흗흘흙흠흡흣흥흩희흰흴흼흽힁히힉힌힐힘힙힛힝"],["caa1","伽佳假價加可呵哥嘉嫁家暇架枷柯歌珂痂稼苛茄街袈訶賈跏軻迦駕刻却各恪慤殼珏脚覺角閣侃刊墾奸姦干幹懇揀杆柬桿澗癎看磵稈竿簡肝艮艱諫間乫喝曷渴碣竭葛褐蝎鞨勘坎堪嵌感憾戡敢柑橄減甘疳監瞰紺邯鑑鑒龕"],["cba1","匣岬甲胛鉀閘剛堈姜岡崗康强彊慷江畺疆糠絳綱羌腔舡薑襁講鋼降鱇介价個凱塏愷愾慨改槪漑疥皆盖箇芥蓋豈鎧開喀客坑更粳羹醵倨去居巨拒据據擧渠炬祛距踞車遽鉅鋸乾件健巾建愆楗腱虔蹇鍵騫乞傑杰桀儉劍劒檢"],["cca1","瞼鈐黔劫怯迲偈憩揭擊格檄激膈覡隔堅牽犬甄絹繭肩見譴遣鵑抉決潔結缺訣兼慊箝謙鉗鎌京俓倞傾儆勁勍卿坰境庚徑慶憬擎敬景暻更梗涇炅烱璟璥瓊痙硬磬竟競絅經耕耿脛莖警輕逕鏡頃頸驚鯨係啓堺契季屆悸戒桂械"],["cda1","棨溪界癸磎稽系繫繼計誡谿階鷄古叩告呱固姑孤尻庫拷攷故敲暠枯槁沽痼皐睾稿羔考股膏苦苽菰藁蠱袴誥賈辜錮雇顧高鼓哭斛曲梏穀谷鵠困坤崑昆梱棍滾琨袞鯤汨滑骨供公共功孔工恐恭拱控攻珙空蚣貢鞏串寡戈果瓜"],["cea1","科菓誇課跨過鍋顆廓槨藿郭串冠官寬慣棺款灌琯瓘管罐菅觀貫關館刮恝括适侊光匡壙廣曠洸炚狂珖筐胱鑛卦掛罫乖傀塊壞怪愧拐槐魁宏紘肱轟交僑咬喬嬌嶠巧攪敎校橋狡皎矯絞翹膠蕎蛟較轎郊餃驕鮫丘久九仇俱具勾"],["cfa1","區口句咎嘔坵垢寇嶇廐懼拘救枸柩構歐毆毬求溝灸狗玖球瞿矩究絿耉臼舅舊苟衢謳購軀逑邱鉤銶駒驅鳩鷗龜國局菊鞠鞫麴君窘群裙軍郡堀屈掘窟宮弓穹窮芎躬倦券勸卷圈拳捲權淃眷厥獗蕨蹶闕机櫃潰詭軌饋句晷歸貴"],["d0a1","鬼龜叫圭奎揆槻珪硅窺竅糾葵規赳逵閨勻均畇筠菌鈞龜橘克剋劇戟棘極隙僅劤勤懃斤根槿瑾筋芹菫覲謹近饉契今妗擒昑檎琴禁禽芩衾衿襟金錦伋及急扱汲級給亘兢矜肯企伎其冀嗜器圻基埼夔奇妓寄岐崎己幾忌技旗旣"],["d1a1","朞期杞棋棄機欺氣汽沂淇玘琦琪璂璣畸畿碁磯祁祇祈祺箕紀綺羈耆耭肌記譏豈起錡錤飢饑騎騏驥麒緊佶吉拮桔金喫儺喇奈娜懦懶拏拿癩",5,"那樂",4,"諾酪駱亂卵暖欄煖爛蘭難鸞捏捺南嵐枏楠湳濫男藍襤拉"],["d2a1","納臘蠟衲囊娘廊",4,"乃來內奈柰耐冷女年撚秊念恬拈捻寧寗努勞奴弩怒擄櫓爐瑙盧",5,"駑魯",10,"濃籠聾膿農惱牢磊腦賂雷尿壘",7,"嫩訥杻紐勒",5,"能菱陵尼泥匿溺多茶"],["d3a1","丹亶但單團壇彖斷旦檀段湍短端簞緞蛋袒鄲鍛撻澾獺疸達啖坍憺擔曇淡湛潭澹痰聃膽蕁覃談譚錟沓畓答踏遝唐堂塘幢戇撞棠當糖螳黨代垈坮大對岱帶待戴擡玳臺袋貸隊黛宅德悳倒刀到圖堵塗導屠島嶋度徒悼挑掉搗桃"],["d4a1","棹櫂淘渡滔濤燾盜睹禱稻萄覩賭跳蹈逃途道都鍍陶韜毒瀆牘犢獨督禿篤纛讀墩惇敦旽暾沌焞燉豚頓乭突仝冬凍動同憧東桐棟洞潼疼瞳童胴董銅兜斗杜枓痘竇荳讀豆逗頭屯臀芚遁遯鈍得嶝橙燈登等藤謄鄧騰喇懶拏癩羅"],["d5a1","蘿螺裸邏樂洛烙珞絡落諾酪駱丹亂卵欄欒瀾爛蘭鸞剌辣嵐擥攬欖濫籃纜藍襤覽拉臘蠟廊朗浪狼琅瑯螂郞來崍徠萊冷掠略亮倆兩凉梁樑粮粱糧良諒輛量侶儷勵呂廬慮戾旅櫚濾礪藜蠣閭驢驪麗黎力曆歷瀝礫轢靂憐戀攣漣"],["d6a1","煉璉練聯蓮輦連鍊冽列劣洌烈裂廉斂殮濂簾獵令伶囹寧岺嶺怜玲笭羚翎聆逞鈴零靈領齡例澧禮醴隷勞怒撈擄櫓潞瀘爐盧老蘆虜路輅露魯鷺鹵碌祿綠菉錄鹿麓論壟弄朧瀧瓏籠聾儡瀨牢磊賂賚賴雷了僚寮廖料燎療瞭聊蓼"],["d7a1","遼鬧龍壘婁屢樓淚漏瘻累縷蔞褸鏤陋劉旒柳榴流溜瀏琉瑠留瘤硫謬類六戮陸侖倫崙淪綸輪律慄栗率隆勒肋凜凌楞稜綾菱陵俚利厘吏唎履悧李梨浬犁狸理璃異痢籬罹羸莉裏裡里釐離鯉吝潾燐璘藺躪隣鱗麟林淋琳臨霖砬"],["d8a1","立笠粒摩瑪痲碼磨馬魔麻寞幕漠膜莫邈万卍娩巒彎慢挽晩曼滿漫灣瞞萬蔓蠻輓饅鰻唜抹末沫茉襪靺亡妄忘忙望網罔芒茫莽輞邙埋妹媒寐昧枚梅每煤罵買賣邁魅脈貊陌驀麥孟氓猛盲盟萌冪覓免冕勉棉沔眄眠綿緬面麵滅"],["d9a1","蔑冥名命明暝椧溟皿瞑茗蓂螟酩銘鳴袂侮冒募姆帽慕摸摹暮某模母毛牟牡瑁眸矛耗芼茅謀謨貌木沐牧目睦穆鶩歿沒夢朦蒙卯墓妙廟描昴杳渺猫竗苗錨務巫憮懋戊拇撫无楙武毋無珷畝繆舞茂蕪誣貿霧鵡墨默們刎吻問文"],["daa1","汶紊紋聞蚊門雯勿沕物味媚尾嵋彌微未梶楣渼湄眉米美薇謎迷靡黴岷悶愍憫敏旻旼民泯玟珉緡閔密蜜謐剝博拍搏撲朴樸泊珀璞箔粕縛膊舶薄迫雹駁伴半反叛拌搬攀斑槃泮潘班畔瘢盤盼磐磻礬絆般蟠返頒飯勃拔撥渤潑"],["dba1","發跋醱鉢髮魃倣傍坊妨尨幇彷房放方旁昉枋榜滂磅紡肪膀舫芳蒡蚌訪謗邦防龐倍俳北培徘拜排杯湃焙盃背胚裴裵褙賠輩配陪伯佰帛柏栢白百魄幡樊煩燔番磻繁蕃藩飜伐筏罰閥凡帆梵氾汎泛犯範范法琺僻劈壁擘檗璧癖"],["dca1","碧蘗闢霹便卞弁變辨辯邊別瞥鱉鼈丙倂兵屛幷昞昺柄棅炳甁病秉竝輧餠騈保堡報寶普步洑湺潽珤甫菩補褓譜輔伏僕匐卜宓復服福腹茯蔔複覆輹輻馥鰒本乶俸奉封峯峰捧棒烽熢琫縫蓬蜂逢鋒鳳不付俯傅剖副否咐埠夫婦"],["dda1","孚孵富府復扶敷斧浮溥父符簿缶腐腑膚艀芙莩訃負賦賻赴趺部釜阜附駙鳧北分吩噴墳奔奮忿憤扮昐汾焚盆粉糞紛芬賁雰不佛弗彿拂崩朋棚硼繃鵬丕備匕匪卑妃婢庇悲憊扉批斐枇榧比毖毗毘沸泌琵痺砒碑秕秘粃緋翡肥"],["dea1","脾臂菲蜚裨誹譬費鄙非飛鼻嚬嬪彬斌檳殯浜濱瀕牝玭貧賓頻憑氷聘騁乍事些仕伺似使俟僿史司唆嗣四士奢娑寫寺射巳師徙思捨斜斯柶査梭死沙泗渣瀉獅砂社祀祠私篩紗絲肆舍莎蓑蛇裟詐詞謝賜赦辭邪飼駟麝削數朔索"],["dfa1","傘刪山散汕珊産疝算蒜酸霰乷撒殺煞薩三參杉森渗芟蔘衫揷澁鈒颯上傷像償商喪嘗孀尙峠常床庠廂想桑橡湘爽牀狀相祥箱翔裳觴詳象賞霜塞璽賽嗇塞穡索色牲生甥省笙墅壻嶼序庶徐恕抒捿敍暑曙書栖棲犀瑞筮絮緖署"],["e0a1","胥舒薯西誓逝鋤黍鼠夕奭席惜昔晳析汐淅潟石碩蓆釋錫仙僊先善嬋宣扇敾旋渲煽琁瑄璇璿癬禪線繕羨腺膳船蘚蟬詵跣選銑鐥饍鮮卨屑楔泄洩渫舌薛褻設說雪齧剡暹殲纖蟾贍閃陝攝涉燮葉城姓宬性惺成星晟猩珹盛省筬"],["e1a1","聖聲腥誠醒世勢歲洗稅笹細說貰召嘯塑宵小少巢所掃搔昭梳沼消溯瀟炤燒甦疏疎瘙笑篠簫素紹蔬蕭蘇訴逍遡邵銷韶騷俗屬束涑粟續謖贖速孫巽損蓀遜飡率宋悚松淞訟誦送頌刷殺灑碎鎖衰釗修受嗽囚垂壽嫂守岫峀帥愁"],["e2a1","戍手授搜收數樹殊水洙漱燧狩獸琇璲瘦睡秀穗竪粹綏綬繡羞脩茱蒐蓚藪袖誰讐輸遂邃酬銖銹隋隧隨雖需須首髓鬚叔塾夙孰宿淑潚熟琡璹肅菽巡徇循恂旬栒楯橓殉洵淳珣盾瞬筍純脣舜荀蓴蕣詢諄醇錞順馴戌術述鉥崇崧"],["e3a1","嵩瑟膝蝨濕拾習褶襲丞乘僧勝升承昇繩蠅陞侍匙嘶始媤尸屎屍市弑恃施是時枾柴猜矢示翅蒔蓍視試詩諡豕豺埴寔式息拭植殖湜熄篒蝕識軾食飾伸侁信呻娠宸愼新晨燼申神紳腎臣莘薪藎蜃訊身辛辰迅失室實悉審尋心沁"],["e4a1","沈深瀋甚芯諶什十拾雙氏亞俄兒啞娥峨我牙芽莪蛾衙訝阿雅餓鴉鵝堊岳嶽幄惡愕握樂渥鄂鍔顎鰐齷安岸按晏案眼雁鞍顔鮟斡謁軋閼唵岩巖庵暗癌菴闇壓押狎鴨仰央怏昻殃秧鴦厓哀埃崖愛曖涯碍艾隘靄厄扼掖液縊腋額"],["e5a1","櫻罌鶯鸚也倻冶夜惹揶椰爺耶若野弱掠略約若葯蒻藥躍亮佯兩凉壤孃恙揚攘敭暘梁楊樣洋瀁煬痒瘍禳穰糧羊良襄諒讓釀陽量養圄御於漁瘀禦語馭魚齬億憶抑檍臆偃堰彦焉言諺孼蘖俺儼嚴奄掩淹嶪業円予余勵呂女如廬"],["e6a1","旅歟汝濾璵礖礪與艅茹輿轝閭餘驪麗黎亦力域役易曆歷疫繹譯轢逆驛嚥堧姸娟宴年延憐戀捐挻撚椽沇沿涎涓淵演漣烟然煙煉燃燕璉硏硯秊筵緣練縯聯衍軟輦蓮連鉛鍊鳶列劣咽悅涅烈熱裂說閱厭廉念捻染殮炎焰琰艶苒"],["e7a1","簾閻髥鹽曄獵燁葉令囹塋寧嶺嶸影怜映暎楹榮永泳渶潁濚瀛瀯煐營獰玲瑛瑩瓔盈穎纓羚聆英詠迎鈴鍈零霙靈領乂倪例刈叡曳汭濊猊睿穢芮藝蘂禮裔詣譽豫醴銳隸霓預五伍俉傲午吾吳嗚塢墺奧娛寤悟惡懊敖旿晤梧汚澳"],["e8a1","烏熬獒筽蜈誤鰲鼇屋沃獄玉鈺溫瑥瘟穩縕蘊兀壅擁瓮甕癰翁邕雍饔渦瓦窩窪臥蛙蝸訛婉完宛梡椀浣玩琓琬碗緩翫脘腕莞豌阮頑曰往旺枉汪王倭娃歪矮外嵬巍猥畏了僚僥凹堯夭妖姚寥寮尿嶢拗搖撓擾料曜樂橈燎燿瑤療"],["e9a1","窈窯繇繞耀腰蓼蟯要謠遙遼邀饒慾欲浴縟褥辱俑傭冗勇埇墉容庸慂榕涌湧溶熔瑢用甬聳茸蓉踊鎔鏞龍于佑偶優又友右宇寓尤愚憂旴牛玗瑀盂祐禑禹紆羽芋藕虞迂遇郵釪隅雨雩勖彧旭昱栯煜稶郁頊云暈橒殞澐熉耘芸蕓"],["eaa1","運隕雲韻蔚鬱亐熊雄元原員圓園垣媛嫄寃怨愿援沅洹湲源爰猿瑗苑袁轅遠阮院願鴛月越鉞位偉僞危圍委威尉慰暐渭爲瑋緯胃萎葦蔿蝟衛褘謂違韋魏乳侑儒兪劉唯喩孺宥幼幽庾悠惟愈愉揄攸有杻柔柚柳楡楢油洧流游溜"],["eba1","濡猶猷琉瑜由留癒硫紐維臾萸裕誘諛諭踰蹂遊逾遺酉釉鍮類六堉戮毓肉育陸倫允奫尹崙淪潤玧胤贇輪鈗閏律慄栗率聿戎瀜絨融隆垠恩慇殷誾銀隱乙吟淫蔭陰音飮揖泣邑凝應膺鷹依倚儀宜意懿擬椅毅疑矣義艤薏蟻衣誼"],["eca1","議醫二以伊利吏夷姨履已弛彛怡易李梨泥爾珥理異痍痢移罹而耳肄苡荑裏裡貽貳邇里離飴餌匿溺瀷益翊翌翼謚人仁刃印吝咽因姻寅引忍湮燐璘絪茵藺蚓認隣靭靷鱗麟一佚佾壹日溢逸鎰馹任壬妊姙恁林淋稔臨荏賃入卄"],["eda1","立笠粒仍剩孕芿仔刺咨姉姿子字孜恣慈滋炙煮玆瓷疵磁紫者自茨蔗藉諮資雌作勺嚼斫昨灼炸爵綽芍酌雀鵲孱棧殘潺盞岑暫潛箴簪蠶雜丈仗匠場墻壯奬將帳庄張掌暲杖樟檣欌漿牆狀獐璋章粧腸臟臧莊葬蔣薔藏裝贓醬長"],["eea1","障再哉在宰才材栽梓渽滓災縡裁財載齋齎爭箏諍錚佇低儲咀姐底抵杵楮樗沮渚狙猪疽箸紵苧菹著藷詛貯躇這邸雎齟勣吊嫡寂摘敵滴狄炙的積笛籍績翟荻謫賊赤跡蹟迪迹適鏑佃佺傳全典前剪塡塼奠專展廛悛戰栓殿氈澱"],["efa1","煎琠田甸畑癲筌箋箭篆纏詮輾轉鈿銓錢鐫電顚顫餞切截折浙癤竊節絶占岾店漸点粘霑鮎點接摺蝶丁井亭停偵呈姃定幀庭廷征情挺政整旌晶晸柾楨檉正汀淀淨渟湞瀞炡玎珽町睛碇禎程穽精綎艇訂諪貞鄭酊釘鉦鋌錠霆靖"],["f0a1","靜頂鼎制劑啼堤帝弟悌提梯濟祭第臍薺製諸蹄醍除際霽題齊俎兆凋助嘲弔彫措操早晁曺曹朝條棗槽漕潮照燥爪璪眺祖祚租稠窕粗糟組繰肇藻蚤詔調趙躁造遭釣阻雕鳥族簇足鏃存尊卒拙猝倧宗從悰慫棕淙琮種終綜縱腫"],["f1a1","踪踵鍾鐘佐坐左座挫罪主住侏做姝胄呪周嗾奏宙州廚晝朱柱株注洲湊澍炷珠疇籌紂紬綢舟蛛註誅走躊輳週酎酒鑄駐竹粥俊儁准埈寯峻晙樽浚準濬焌畯竣蠢逡遵雋駿茁中仲衆重卽櫛楫汁葺增憎曾拯烝甑症繒蒸證贈之只"],["f2a1","咫地址志持指摯支旨智枝枳止池沚漬知砥祉祗紙肢脂至芝芷蜘誌識贄趾遲直稙稷織職唇嗔塵振搢晉晋桭榛殄津溱珍瑨璡畛疹盡眞瞋秦縉縝臻蔯袗診賑軫辰進鎭陣陳震侄叱姪嫉帙桎瓆疾秩窒膣蛭質跌迭斟朕什執潗緝輯"],["f3a1","鏶集徵懲澄且侘借叉嗟嵯差次此磋箚茶蹉車遮捉搾着窄錯鑿齪撰澯燦璨瓚竄簒纂粲纘讚贊鑽餐饌刹察擦札紮僭參塹慘慙懺斬站讒讖倉倡創唱娼廠彰愴敞昌昶暢槍滄漲猖瘡窓脹艙菖蒼債埰寀寨彩採砦綵菜蔡采釵冊柵策"],["f4a1","責凄妻悽處倜刺剔尺慽戚拓擲斥滌瘠脊蹠陟隻仟千喘天川擅泉淺玔穿舛薦賤踐遷釧闡阡韆凸哲喆徹撤澈綴輟轍鐵僉尖沾添甛瞻簽籤詹諂堞妾帖捷牒疊睫諜貼輒廳晴淸聽菁請靑鯖切剃替涕滯締諦逮遞體初剿哨憔抄招梢"],["f5a1","椒楚樵炒焦硝礁礎秒稍肖艸苕草蕉貂超酢醋醮促囑燭矗蜀觸寸忖村邨叢塚寵悤憁摠總聰蔥銃撮催崔最墜抽推椎楸樞湫皺秋芻萩諏趨追鄒酋醜錐錘鎚雛騶鰍丑畜祝竺筑築縮蓄蹙蹴軸逐春椿瑃出朮黜充忠沖蟲衝衷悴膵萃"],["f6a1","贅取吹嘴娶就炊翠聚脆臭趣醉驟鷲側仄厠惻測層侈値嗤峙幟恥梔治淄熾痔痴癡稚穉緇緻置致蚩輜雉馳齒則勅飭親七柒漆侵寢枕沈浸琛砧針鍼蟄秤稱快他咤唾墮妥惰打拖朶楕舵陀馱駝倬卓啄坼度托拓擢晫柝濁濯琢琸託"],["f7a1","鐸呑嘆坦彈憚歎灘炭綻誕奪脫探眈耽貪塔搭榻宕帑湯糖蕩兌台太怠態殆汰泰笞胎苔跆邰颱宅擇澤撑攄兎吐土討慟桶洞痛筒統通堆槌腿褪退頹偸套妬投透鬪慝特闖坡婆巴把播擺杷波派爬琶破罷芭跛頗判坂板版瓣販辦鈑"],["f8a1","阪八叭捌佩唄悖敗沛浿牌狽稗覇貝彭澎烹膨愎便偏扁片篇編翩遍鞭騙貶坪平枰萍評吠嬖幣廢弊斃肺蔽閉陛佈包匍匏咆哺圃布怖抛抱捕暴泡浦疱砲胞脯苞葡蒲袍褒逋鋪飽鮑幅暴曝瀑爆輻俵剽彪慓杓標漂瓢票表豹飇飄驃"],["f9a1","品稟楓諷豊風馮彼披疲皮被避陂匹弼必泌珌畢疋筆苾馝乏逼下何厦夏廈昰河瑕荷蝦賀遐霞鰕壑學虐謔鶴寒恨悍旱汗漢澣瀚罕翰閑閒限韓割轄函含咸啣喊檻涵緘艦銜陷鹹合哈盒蛤閤闔陜亢伉姮嫦巷恒抗杭桁沆港缸肛航"],["faa1","行降項亥偕咳垓奚孩害懈楷海瀣蟹解該諧邂駭骸劾核倖幸杏荇行享向嚮珦鄕響餉饗香噓墟虛許憲櫶獻軒歇險驗奕爀赫革俔峴弦懸晛泫炫玄玹現眩睍絃絢縣舷衒見賢鉉顯孑穴血頁嫌俠協夾峽挾浹狹脅脇莢鋏頰亨兄刑型"],["fba1","形泂滎瀅灐炯熒珩瑩荊螢衡逈邢鎣馨兮彗惠慧暳蕙蹊醯鞋乎互呼壕壺好岵弧戶扈昊晧毫浩淏湖滸澔濠濩灝狐琥瑚瓠皓祜糊縞胡芦葫蒿虎號蝴護豪鎬頀顥惑或酷婚昏混渾琿魂忽惚笏哄弘汞泓洪烘紅虹訌鴻化和嬅樺火畵"],["fca1","禍禾花華話譁貨靴廓擴攫確碻穫丸喚奐宦幻患換歡晥桓渙煥環紈還驩鰥活滑猾豁闊凰幌徨恍惶愰慌晃晄榥況湟滉潢煌璜皇篁簧荒蝗遑隍黃匯回廻徊恢悔懷晦會檜淮澮灰獪繪膾茴蛔誨賄劃獲宖橫鐄哮嚆孝效斅曉梟涍淆"],["fda1","爻肴酵驍侯候厚后吼喉嗅帿後朽煦珝逅勛勳塤壎焄熏燻薰訓暈薨喧暄煊萱卉喙毁彙徽揮暉煇諱輝麾休携烋畦虧恤譎鷸兇凶匈洶胸黑昕欣炘痕吃屹紇訖欠欽歆吸恰洽翕興僖凞喜噫囍姬嬉希憙憘戱晞曦熙熹熺犧禧稀羲詰"]]');

/***/ }),

/***/ 72324:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["0","\\u0000",127],["a140","　，、。．‧；：？！︰…‥﹐﹑﹒·﹔﹕﹖﹗｜–︱—︳╴︴﹏（）︵︶｛｝︷︸〔〕︹︺【】︻︼《》︽︾〈〉︿﹀「」﹁﹂『』﹃﹄﹙﹚"],["a1a1","﹛﹜﹝﹞‘’“”〝〞‵′＃＆＊※§〃○●△▲◎☆★◇◆□■▽▼㊣℅¯￣＿ˍ﹉﹊﹍﹎﹋﹌﹟﹠﹡＋－×÷±√＜＞＝≦≧≠∞≒≡﹢",4,"～∩∪⊥∠∟⊿㏒㏑∫∮∵∴♀♂⊕⊙↑↓←→↖↗↙↘∥∣／"],["a240","＼∕﹨＄￥〒￠￡％＠℃℉﹩﹪﹫㏕㎜㎝㎞㏎㎡㎎㎏㏄°兙兛兞兝兡兣嗧瓩糎▁",7,"▏▎▍▌▋▊▉┼┴┬┤├▔─│▕┌┐└┘╭"],["a2a1","╮╰╯═╞╪╡◢◣◥◤╱╲╳０",9,"Ⅰ",9,"〡",8,"十卄卅Ａ",25,"ａ",21],["a340","ｗｘｙｚΑ",16,"Σ",6,"α",16,"σ",6,"ㄅ",10],["a3a1","ㄐ",25,"˙ˉˊˇˋ"],["a3e1","€"],["a440","一乙丁七乃九了二人儿入八几刀刁力匕十卜又三下丈上丫丸凡久么也乞于亡兀刃勺千叉口土士夕大女子孑孓寸小尢尸山川工己已巳巾干廾弋弓才"],["a4a1","丑丐不中丰丹之尹予云井互五亢仁什仃仆仇仍今介仄元允內六兮公冗凶分切刈勻勾勿化匹午升卅卞厄友及反壬天夫太夭孔少尤尺屯巴幻廿弔引心戈戶手扎支文斗斤方日曰月木欠止歹毋比毛氏水火爪父爻片牙牛犬王丙"],["a540","世丕且丘主乍乏乎以付仔仕他仗代令仙仞充兄冉冊冬凹出凸刊加功包匆北匝仟半卉卡占卯卮去可古右召叮叩叨叼司叵叫另只史叱台句叭叻四囚外"],["a5a1","央失奴奶孕它尼巨巧左市布平幼弁弘弗必戊打扔扒扑斥旦朮本未末札正母民氐永汁汀氾犯玄玉瓜瓦甘生用甩田由甲申疋白皮皿目矛矢石示禾穴立丞丟乒乓乩亙交亦亥仿伉伙伊伕伍伐休伏仲件任仰仳份企伋光兇兆先全"],["a640","共再冰列刑划刎刖劣匈匡匠印危吉吏同吊吐吁吋各向名合吃后吆吒因回囝圳地在圭圬圯圩夙多夷夸妄奸妃好她如妁字存宇守宅安寺尖屹州帆并年"],["a6a1","式弛忙忖戎戌戍成扣扛托收早旨旬旭曲曳有朽朴朱朵次此死氖汝汗汙江池汐汕污汛汍汎灰牟牝百竹米糸缶羊羽老考而耒耳聿肉肋肌臣自至臼舌舛舟艮色艾虫血行衣西阡串亨位住佇佗佞伴佛何估佐佑伽伺伸佃佔似但佣"],["a740","作你伯低伶余佝佈佚兌克免兵冶冷別判利刪刨劫助努劬匣即卵吝吭吞吾否呎吧呆呃吳呈呂君吩告吹吻吸吮吵吶吠吼呀吱含吟听囪困囤囫坊坑址坍"],["a7a1","均坎圾坐坏圻壯夾妝妒妨妞妣妙妖妍妤妓妊妥孝孜孚孛完宋宏尬局屁尿尾岐岑岔岌巫希序庇床廷弄弟彤形彷役忘忌志忍忱快忸忪戒我抄抗抖技扶抉扭把扼找批扳抒扯折扮投抓抑抆改攻攸旱更束李杏材村杜杖杞杉杆杠"],["a840","杓杗步每求汞沙沁沈沉沅沛汪決沐汰沌汨沖沒汽沃汲汾汴沆汶沍沔沘沂灶灼災灸牢牡牠狄狂玖甬甫男甸皂盯矣私秀禿究系罕肖肓肝肘肛肚育良芒"],["a8a1","芋芍見角言谷豆豕貝赤走足身車辛辰迂迆迅迄巡邑邢邪邦那酉釆里防阮阱阪阬並乖乳事些亞享京佯依侍佳使佬供例來侃佰併侈佩佻侖佾侏侑佺兔兒兕兩具其典冽函刻券刷刺到刮制剁劾劻卒協卓卑卦卷卸卹取叔受味呵"],["a940","咖呸咕咀呻呷咄咒咆呼咐呱呶和咚呢周咋命咎固垃坷坪坩坡坦坤坼夜奉奇奈奄奔妾妻委妹妮姑姆姐姍始姓姊妯妳姒姅孟孤季宗定官宜宙宛尚屈居"],["a9a1","屆岷岡岸岩岫岱岳帘帚帖帕帛帑幸庚店府底庖延弦弧弩往征彿彼忝忠忽念忿怏怔怯怵怖怪怕怡性怩怫怛或戕房戾所承拉拌拄抿拂抹拒招披拓拔拋拈抨抽押拐拙拇拍抵拚抱拘拖拗拆抬拎放斧於旺昔易昌昆昂明昀昏昕昊"],["aa40","昇服朋杭枋枕東果杳杷枇枝林杯杰板枉松析杵枚枓杼杪杲欣武歧歿氓氛泣注泳沱泌泥河沽沾沼波沫法泓沸泄油況沮泗泅泱沿治泡泛泊沬泯泜泖泠"],["aaa1","炕炎炒炊炙爬爭爸版牧物狀狎狙狗狐玩玨玟玫玥甽疝疙疚的盂盲直知矽社祀祁秉秈空穹竺糾罔羌羋者肺肥肢肱股肫肩肴肪肯臥臾舍芳芝芙芭芽芟芹花芬芥芯芸芣芰芾芷虎虱初表軋迎返近邵邸邱邶采金長門阜陀阿阻附"],["ab40","陂隹雨青非亟亭亮信侵侯便俠俑俏保促侶俘俟俊俗侮俐俄係俚俎俞侷兗冒冑冠剎剃削前剌剋則勇勉勃勁匍南卻厚叛咬哀咨哎哉咸咦咳哇哂咽咪品"],["aba1","哄哈咯咫咱咻咩咧咿囿垂型垠垣垢城垮垓奕契奏奎奐姜姘姿姣姨娃姥姪姚姦威姻孩宣宦室客宥封屎屏屍屋峙峒巷帝帥帟幽庠度建弈弭彥很待徊律徇後徉怒思怠急怎怨恍恰恨恢恆恃恬恫恪恤扁拜挖按拼拭持拮拽指拱拷"],["ac40","拯括拾拴挑挂政故斫施既春昭映昧是星昨昱昤曷柿染柱柔某柬架枯柵柩柯柄柑枴柚查枸柏柞柳枰柙柢柝柒歪殃殆段毒毗氟泉洋洲洪流津洌洱洞洗"],["aca1","活洽派洶洛泵洹洧洸洩洮洵洎洫炫為炳炬炯炭炸炮炤爰牲牯牴狩狠狡玷珊玻玲珍珀玳甚甭畏界畎畋疫疤疥疢疣癸皆皇皈盈盆盃盅省盹相眉看盾盼眇矜砂研砌砍祆祉祈祇禹禺科秒秋穿突竿竽籽紂紅紀紉紇約紆缸美羿耄"],["ad40","耐耍耑耶胖胥胚胃胄背胡胛胎胞胤胝致舢苧范茅苣苛苦茄若茂茉苒苗英茁苜苔苑苞苓苟苯茆虐虹虻虺衍衫要觔計訂訃貞負赴赳趴軍軌述迦迢迪迥"],["ada1","迭迫迤迨郊郎郁郃酋酊重閂限陋陌降面革韋韭音頁風飛食首香乘亳倌倍倣俯倦倥俸倩倖倆值借倚倒們俺倀倔倨俱倡個候倘俳修倭倪俾倫倉兼冤冥冢凍凌准凋剖剜剔剛剝匪卿原厝叟哨唐唁唷哼哥哲唆哺唔哩哭員唉哮哪"],["ae40","哦唧唇哽唏圃圄埂埔埋埃堉夏套奘奚娑娘娜娟娛娓姬娠娣娩娥娌娉孫屘宰害家宴宮宵容宸射屑展屐峭峽峻峪峨峰島崁峴差席師庫庭座弱徒徑徐恙"],["aea1","恣恥恐恕恭恩息悄悟悚悍悔悌悅悖扇拳挈拿捎挾振捕捂捆捏捉挺捐挽挪挫挨捍捌效敉料旁旅時晉晏晃晒晌晅晁書朔朕朗校核案框桓根桂桔栩梳栗桌桑栽柴桐桀格桃株桅栓栘桁殊殉殷氣氧氨氦氤泰浪涕消涇浦浸海浙涓"],["af40","浬涉浮浚浴浩涌涊浹涅浥涔烊烘烤烙烈烏爹特狼狹狽狸狷玆班琉珮珠珪珞畔畝畜畚留疾病症疲疳疽疼疹痂疸皋皰益盍盎眩真眠眨矩砰砧砸砝破砷"],["afa1","砥砭砠砟砲祕祐祠祟祖神祝祗祚秤秣秧租秦秩秘窄窈站笆笑粉紡紗紋紊素索純紐紕級紜納紙紛缺罟羔翅翁耆耘耕耙耗耽耿胱脂胰脅胭胴脆胸胳脈能脊胼胯臭臬舀舐航舫舨般芻茫荒荔荊茸荐草茵茴荏茲茹茶茗荀茱茨荃"],["b040","虔蚊蚪蚓蚤蚩蚌蚣蚜衰衷袁袂衽衹記訐討訌訕訊託訓訖訏訑豈豺豹財貢起躬軒軔軏辱送逆迷退迺迴逃追逅迸邕郡郝郢酒配酌釘針釗釜釙閃院陣陡"],["b0a1","陛陝除陘陞隻飢馬骨高鬥鬲鬼乾偺偽停假偃偌做偉健偶偎偕偵側偷偏倏偯偭兜冕凰剪副勒務勘動匐匏匙匿區匾參曼商啪啦啄啞啡啃啊唱啖問啕唯啤唸售啜唬啣唳啁啗圈國圉域堅堊堆埠埤基堂堵執培夠奢娶婁婉婦婪婀"],["b140","娼婢婚婆婊孰寇寅寄寂宿密尉專將屠屜屝崇崆崎崛崖崢崑崩崔崙崤崧崗巢常帶帳帷康庸庶庵庾張強彗彬彩彫得徙從徘御徠徜恿患悉悠您惋悴惦悽"],["b1a1","情悻悵惜悼惘惕惆惟悸惚惇戚戛扈掠控捲掖探接捷捧掘措捱掩掉掃掛捫推掄授掙採掬排掏掀捻捩捨捺敝敖救教敗啟敏敘敕敔斜斛斬族旋旌旎晝晚晤晨晦晞曹勗望梁梯梢梓梵桿桶梱梧梗械梃棄梭梆梅梔條梨梟梡梂欲殺"],["b240","毫毬氫涎涼淳淙液淡淌淤添淺清淇淋涯淑涮淞淹涸混淵淅淒渚涵淚淫淘淪深淮淨淆淄涪淬涿淦烹焉焊烽烯爽牽犁猜猛猖猓猙率琅琊球理現琍瓠瓶"],["b2a1","瓷甜產略畦畢異疏痔痕疵痊痍皎盔盒盛眷眾眼眶眸眺硫硃硎祥票祭移窒窕笠笨笛第符笙笞笮粒粗粕絆絃統紮紹紼絀細紳組累終紲紱缽羞羚翌翎習耜聊聆脯脖脣脫脩脰脤舂舵舷舶船莎莞莘荸莢莖莽莫莒莊莓莉莠荷荻荼"],["b340","莆莧處彪蛇蛀蚶蛄蚵蛆蛋蚱蚯蛉術袞袈被袒袖袍袋覓規訪訝訣訥許設訟訛訢豉豚販責貫貨貪貧赧赦趾趺軛軟這逍通逗連速逝逐逕逞造透逢逖逛途"],["b3a1","部郭都酗野釵釦釣釧釭釩閉陪陵陳陸陰陴陶陷陬雀雪雩章竟頂頃魚鳥鹵鹿麥麻傢傍傅備傑傀傖傘傚最凱割剴創剩勞勝勛博厥啻喀喧啼喊喝喘喂喜喪喔喇喋喃喳單喟唾喲喚喻喬喱啾喉喫喙圍堯堪場堤堰報堡堝堠壹壺奠"],["b440","婷媚婿媒媛媧孳孱寒富寓寐尊尋就嵌嵐崴嵇巽幅帽幀幃幾廊廁廂廄弼彭復循徨惑惡悲悶惠愜愣惺愕惰惻惴慨惱愎惶愉愀愒戟扉掣掌描揀揩揉揆揍"],["b4a1","插揣提握揖揭揮捶援揪換摒揚揹敞敦敢散斑斐斯普晰晴晶景暑智晾晷曾替期朝棺棕棠棘棗椅棟棵森棧棹棒棲棣棋棍植椒椎棉棚楮棻款欺欽殘殖殼毯氮氯氬港游湔渡渲湧湊渠渥渣減湛湘渤湖湮渭渦湯渴湍渺測湃渝渾滋"],["b540","溉渙湎湣湄湲湩湟焙焚焦焰無然煮焜牌犄犀猶猥猴猩琺琪琳琢琥琵琶琴琯琛琦琨甥甦畫番痢痛痣痙痘痞痠登發皖皓皴盜睏短硝硬硯稍稈程稅稀窘"],["b5a1","窗窖童竣等策筆筐筒答筍筋筏筑粟粥絞結絨絕紫絮絲絡給絢絰絳善翔翕耋聒肅腕腔腋腑腎脹腆脾腌腓腴舒舜菩萃菸萍菠菅萋菁華菱菴著萊菰萌菌菽菲菊萸萎萄菜萇菔菟虛蛟蛙蛭蛔蛛蛤蛐蛞街裁裂袱覃視註詠評詞証詁"],["b640","詔詛詐詆訴診訶詖象貂貯貼貳貽賁費賀貴買貶貿貸越超趁跎距跋跚跑跌跛跆軻軸軼辜逮逵週逸進逶鄂郵鄉郾酣酥量鈔鈕鈣鈉鈞鈍鈐鈇鈑閔閏開閑"],["b6a1","間閒閎隊階隋陽隅隆隍陲隄雁雅雄集雇雯雲韌項順須飧飪飯飩飲飭馮馭黃黍黑亂傭債傲傳僅傾催傷傻傯僇剿剷剽募勦勤勢勣匯嗟嗨嗓嗦嗎嗜嗇嗑嗣嗤嗯嗚嗡嗅嗆嗥嗉園圓塞塑塘塗塚塔填塌塭塊塢塒塋奧嫁嫉嫌媾媽媼"],["b740","媳嫂媲嵩嵯幌幹廉廈弒彙徬微愚意慈感想愛惹愁愈慎慌慄慍愾愴愧愍愆愷戡戢搓搾搞搪搭搽搬搏搜搔損搶搖搗搆敬斟新暗暉暇暈暖暄暘暍會榔業"],["b7a1","楚楷楠楔極椰概楊楨楫楞楓楹榆楝楣楛歇歲毀殿毓毽溢溯滓溶滂源溝滇滅溥溘溼溺溫滑準溜滄滔溪溧溴煎煙煩煤煉照煜煬煦煌煥煞煆煨煖爺牒猷獅猿猾瑯瑚瑕瑟瑞瑁琿瑙瑛瑜當畸瘀痰瘁痲痱痺痿痴痳盞盟睛睫睦睞督"],["b840","睹睪睬睜睥睨睢矮碎碰碗碘碌碉硼碑碓硿祺祿禁萬禽稜稚稠稔稟稞窟窠筷節筠筮筧粱粳粵經絹綑綁綏絛置罩罪署義羨群聖聘肆肄腱腰腸腥腮腳腫"],["b8a1","腹腺腦舅艇蒂葷落萱葵葦葫葉葬葛萼萵葡董葩葭葆虞虜號蛹蜓蜈蜇蜀蛾蛻蜂蜃蜆蜊衙裟裔裙補裘裝裡裊裕裒覜解詫該詳試詩詰誇詼詣誠話誅詭詢詮詬詹詻訾詨豢貊貉賊資賈賄貲賃賂賅跡跟跨路跳跺跪跤跦躲較載軾輊"],["b940","辟農運遊道遂達逼違遐遇遏過遍遑逾遁鄒鄗酬酪酩釉鈷鉗鈸鈽鉀鈾鉛鉋鉤鉑鈴鉉鉍鉅鈹鈿鉚閘隘隔隕雍雋雉雊雷電雹零靖靴靶預頑頓頊頒頌飼飴"],["b9a1","飽飾馳馱馴髡鳩麂鼎鼓鼠僧僮僥僖僭僚僕像僑僱僎僩兢凳劃劂匱厭嗾嘀嘛嘗嗽嘔嘆嘉嘍嘎嗷嘖嘟嘈嘐嗶團圖塵塾境墓墊塹墅塽壽夥夢夤奪奩嫡嫦嫩嫗嫖嫘嫣孵寞寧寡寥實寨寢寤察對屢嶄嶇幛幣幕幗幔廓廖弊彆彰徹慇"],["ba40","愿態慷慢慣慟慚慘慵截撇摘摔撤摸摟摺摑摧搴摭摻敲斡旗旖暢暨暝榜榨榕槁榮槓構榛榷榻榫榴槐槍榭槌榦槃榣歉歌氳漳演滾漓滴漩漾漠漬漏漂漢"],["baa1","滿滯漆漱漸漲漣漕漫漯澈漪滬漁滲滌滷熔熙煽熊熄熒爾犒犖獄獐瑤瑣瑪瑰瑭甄疑瘧瘍瘋瘉瘓盡監瞄睽睿睡磁碟碧碳碩碣禎福禍種稱窪窩竭端管箕箋筵算箝箔箏箸箇箄粹粽精綻綰綜綽綾綠緊綴網綱綺綢綿綵綸維緒緇綬"],["bb40","罰翠翡翟聞聚肇腐膀膏膈膊腿膂臧臺與舔舞艋蓉蒿蓆蓄蒙蒞蒲蒜蓋蒸蓀蓓蒐蒼蓑蓊蜿蜜蜻蜢蜥蜴蜘蝕蜷蜩裳褂裴裹裸製裨褚裯誦誌語誣認誡誓誤"],["bba1","說誥誨誘誑誚誧豪貍貌賓賑賒赫趙趕跼輔輒輕輓辣遠遘遜遣遙遞遢遝遛鄙鄘鄞酵酸酷酴鉸銀銅銘銖鉻銓銜銨鉼銑閡閨閩閣閥閤隙障際雌雒需靼鞅韶頗領颯颱餃餅餌餉駁骯骰髦魁魂鳴鳶鳳麼鼻齊億儀僻僵價儂儈儉儅凜"],["bc40","劇劈劉劍劊勰厲嘮嘻嘹嘲嘿嘴嘩噓噎噗噴嘶嘯嘰墀墟增墳墜墮墩墦奭嬉嫻嬋嫵嬌嬈寮寬審寫層履嶝嶔幢幟幡廢廚廟廝廣廠彈影德徵慶慧慮慝慕憂"],["bca1","慼慰慫慾憧憐憫憎憬憚憤憔憮戮摩摯摹撞撲撈撐撰撥撓撕撩撒撮播撫撚撬撙撢撳敵敷數暮暫暴暱樣樟槨樁樞標槽模樓樊槳樂樅槭樑歐歎殤毅毆漿潼澄潑潦潔澆潭潛潸潮澎潺潰潤澗潘滕潯潠潟熟熬熱熨牖犛獎獗瑩璋璃"],["bd40","瑾璀畿瘠瘩瘟瘤瘦瘡瘢皚皺盤瞎瞇瞌瞑瞋磋磅確磊碾磕碼磐稿稼穀稽稷稻窯窮箭箱範箴篆篇篁箠篌糊締練緯緻緘緬緝編緣線緞緩綞緙緲緹罵罷羯"],["bda1","翩耦膛膜膝膠膚膘蔗蔽蔚蓮蔬蔭蔓蔑蔣蔡蔔蓬蔥蓿蔆螂蝴蝶蝠蝦蝸蝨蝙蝗蝌蝓衛衝褐複褒褓褕褊誼諒談諄誕請諸課諉諂調誰論諍誶誹諛豌豎豬賠賞賦賤賬賭賢賣賜質賡赭趟趣踫踐踝踢踏踩踟踡踞躺輝輛輟輩輦輪輜輞"],["be40","輥適遮遨遭遷鄰鄭鄧鄱醇醉醋醃鋅銻銷鋪銬鋤鋁銳銼鋒鋇鋰銲閭閱霄霆震霉靠鞍鞋鞏頡頫頜颳養餓餒餘駝駐駟駛駑駕駒駙骷髮髯鬧魅魄魷魯鴆鴉"],["bea1","鴃麩麾黎墨齒儒儘儔儐儕冀冪凝劑劓勳噙噫噹噩噤噸噪器噥噱噯噬噢噶壁墾壇壅奮嬝嬴學寰導彊憲憑憩憊懍憶憾懊懈戰擅擁擋撻撼據擄擇擂操撿擒擔撾整曆曉暹曄曇暸樽樸樺橙橫橘樹橄橢橡橋橇樵機橈歙歷氅濂澱澡"],["bf40","濃澤濁澧澳激澹澶澦澠澴熾燉燐燒燈燕熹燎燙燜燃燄獨璜璣璘璟璞瓢甌甍瘴瘸瘺盧盥瞠瞞瞟瞥磨磚磬磧禦積穎穆穌穋窺篙簑築篤篛篡篩篦糕糖縊"],["bfa1","縑縈縛縣縞縝縉縐罹羲翰翱翮耨膳膩膨臻興艘艙蕊蕙蕈蕨蕩蕃蕉蕭蕪蕞螃螟螞螢融衡褪褲褥褫褡親覦諦諺諫諱謀諜諧諮諾謁謂諷諭諳諶諼豫豭貓賴蹄踱踴蹂踹踵輻輯輸輳辨辦遵遴選遲遼遺鄴醒錠錶鋸錳錯錢鋼錫錄錚"],["c040","錐錦錡錕錮錙閻隧隨險雕霎霑霖霍霓霏靛靜靦鞘頰頸頻頷頭頹頤餐館餞餛餡餚駭駢駱骸骼髻髭鬨鮑鴕鴣鴦鴨鴒鴛默黔龍龜優償儡儲勵嚎嚀嚐嚅嚇"],["c0a1","嚏壕壓壑壎嬰嬪嬤孺尷屨嶼嶺嶽嶸幫彌徽應懂懇懦懋戲戴擎擊擘擠擰擦擬擱擢擭斂斃曙曖檀檔檄檢檜櫛檣橾檗檐檠歜殮毚氈濘濱濟濠濛濤濫濯澀濬濡濩濕濮濰燧營燮燦燥燭燬燴燠爵牆獰獲璩環璦璨癆療癌盪瞳瞪瞰瞬"],["c140","瞧瞭矯磷磺磴磯礁禧禪穗窿簇簍篾篷簌篠糠糜糞糢糟糙糝縮績繆縷縲繃縫總縱繅繁縴縹繈縵縿縯罄翳翼聱聲聰聯聳臆臃膺臂臀膿膽臉膾臨舉艱薪"],["c1a1","薄蕾薜薑薔薯薛薇薨薊虧蟀蟑螳蟒蟆螫螻螺蟈蟋褻褶襄褸褽覬謎謗謙講謊謠謝謄謐豁谿豳賺賽購賸賻趨蹉蹋蹈蹊轄輾轂轅輿避遽還邁邂邀鄹醣醞醜鍍鎂錨鍵鍊鍥鍋錘鍾鍬鍛鍰鍚鍔闊闋闌闈闆隱隸雖霜霞鞠韓顆颶餵騁"],["c240","駿鮮鮫鮪鮭鴻鴿麋黏點黜黝黛鼾齋叢嚕嚮壙壘嬸彝懣戳擴擲擾攆擺擻擷斷曜朦檳檬櫃檻檸櫂檮檯歟歸殯瀉瀋濾瀆濺瀑瀏燻燼燾燸獷獵璧璿甕癖癘"],["c2a1","癒瞽瞿瞻瞼礎禮穡穢穠竄竅簫簧簪簞簣簡糧織繕繞繚繡繒繙罈翹翻職聶臍臏舊藏薩藍藐藉薰薺薹薦蟯蟬蟲蟠覆覲觴謨謹謬謫豐贅蹙蹣蹦蹤蹟蹕軀轉轍邇邃邈醫醬釐鎔鎊鎖鎢鎳鎮鎬鎰鎘鎚鎗闔闖闐闕離雜雙雛雞霤鞣鞦"],["c340","鞭韹額顏題顎顓颺餾餿餽餮馥騎髁鬃鬆魏魎魍鯊鯉鯽鯈鯀鵑鵝鵠黠鼕鼬儳嚥壞壟壢寵龐廬懲懷懶懵攀攏曠曝櫥櫝櫚櫓瀛瀟瀨瀚瀝瀕瀘爆爍牘犢獸"],["c3a1","獺璽瓊瓣疇疆癟癡矇礙禱穫穩簾簿簸簽簷籀繫繭繹繩繪羅繳羶羹羸臘藩藝藪藕藤藥藷蟻蠅蠍蟹蟾襠襟襖襞譁譜識證譚譎譏譆譙贈贊蹼蹲躇蹶蹬蹺蹴轔轎辭邊邋醱醮鏡鏑鏟鏃鏈鏜鏝鏖鏢鏍鏘鏤鏗鏨關隴難霪霧靡韜韻類"],["c440","願顛颼饅饉騖騙鬍鯨鯧鯖鯛鶉鵡鵲鵪鵬麒麗麓麴勸嚨嚷嚶嚴嚼壤孀孃孽寶巉懸懺攘攔攙曦朧櫬瀾瀰瀲爐獻瓏癢癥礦礪礬礫竇競籌籃籍糯糰辮繽繼"],["c4a1","纂罌耀臚艦藻藹蘑藺蘆蘋蘇蘊蠔蠕襤覺觸議譬警譯譟譫贏贍躉躁躅躂醴釋鐘鐃鏽闡霰飄饒饑馨騫騰騷騵鰓鰍鹹麵黨鼯齟齣齡儷儸囁囀囂夔屬巍懼懾攝攜斕曩櫻欄櫺殲灌爛犧瓖瓔癩矓籐纏續羼蘗蘭蘚蠣蠢蠡蠟襪襬覽譴"],["c540","護譽贓躊躍躋轟辯醺鐮鐳鐵鐺鐸鐲鐫闢霸霹露響顧顥饗驅驃驀騾髏魔魑鰭鰥鶯鶴鷂鶸麝黯鼙齜齦齧儼儻囈囊囉孿巔巒彎懿攤權歡灑灘玀瓤疊癮癬"],["c5a1","禳籠籟聾聽臟襲襯觼讀贖贗躑躓轡酈鑄鑑鑒霽霾韃韁顫饕驕驍髒鬚鱉鰱鰾鰻鷓鷗鼴齬齪龔囌巖戀攣攫攪曬欐瓚竊籤籣籥纓纖纔臢蘸蘿蠱變邐邏鑣鑠鑤靨顯饜驚驛驗髓體髑鱔鱗鱖鷥麟黴囑壩攬灞癱癲矗罐羈蠶蠹衢讓讒"],["c640","讖艷贛釀鑪靂靈靄韆顰驟鬢魘鱟鷹鷺鹼鹽鼇齷齲廳欖灣籬籮蠻觀躡釁鑲鑰顱饞髖鬣黌灤矚讚鑷韉驢驥纜讜躪釅鑽鑾鑼鱷鱸黷豔鑿鸚爨驪鬱鸛鸞籲"],["c940","乂乜凵匚厂万丌乇亍囗兀屮彳丏冇与丮亓仂仉仈冘勼卬厹圠夃夬尐巿旡殳毌气爿丱丼仨仜仩仡仝仚刌匜卌圢圣夗夯宁宄尒尻屴屳帄庀庂忉戉扐氕"],["c9a1","氶汃氿氻犮犰玊禸肊阞伎优伬仵伔仱伀价伈伝伂伅伢伓伄仴伒冱刓刉刐劦匢匟卍厊吇囡囟圮圪圴夼妀奼妅奻奾奷奿孖尕尥屼屺屻屾巟幵庄异弚彴忕忔忏扜扞扤扡扦扢扙扠扚扥旯旮朾朹朸朻机朿朼朳氘汆汒汜汏汊汔汋"],["ca40","汌灱牞犴犵玎甪癿穵网艸艼芀艽艿虍襾邙邗邘邛邔阢阤阠阣佖伻佢佉体佤伾佧佒佟佁佘伭伳伿佡冏冹刜刞刡劭劮匉卣卲厎厏吰吷吪呔呅吙吜吥吘"],["caa1","吽呏呁吨吤呇囮囧囥坁坅坌坉坋坒夆奀妦妘妠妗妎妢妐妏妧妡宎宒尨尪岍岏岈岋岉岒岊岆岓岕巠帊帎庋庉庌庈庍弅弝彸彶忒忑忐忭忨忮忳忡忤忣忺忯忷忻怀忴戺抃抌抎抏抔抇扱扻扺扰抁抈扷扽扲扴攷旰旴旳旲旵杅杇"],["cb40","杙杕杌杈杝杍杚杋毐氙氚汸汧汫沄沋沏汱汯汩沚汭沇沕沜汦汳汥汻沎灴灺牣犿犽狃狆狁犺狅玕玗玓玔玒町甹疔疕皁礽耴肕肙肐肒肜芐芏芅芎芑芓"],["cba1","芊芃芄豸迉辿邟邡邥邞邧邠阰阨阯阭丳侘佼侅佽侀侇佶佴侉侄佷佌侗佪侚佹侁佸侐侜侔侞侒侂侕佫佮冞冼冾刵刲刳剆刱劼匊匋匼厒厔咇呿咁咑咂咈呫呺呾呥呬呴呦咍呯呡呠咘呣呧呤囷囹坯坲坭坫坱坰坶垀坵坻坳坴坢"],["cc40","坨坽夌奅妵妺姏姎妲姌姁妶妼姃姖妱妽姀姈妴姇孢孥宓宕屄屇岮岤岠岵岯岨岬岟岣岭岢岪岧岝岥岶岰岦帗帔帙弨弢弣弤彔徂彾彽忞忥怭怦怙怲怋"],["cca1","怴怊怗怳怚怞怬怢怍怐怮怓怑怌怉怜戔戽抭抴拑抾抪抶拊抮抳抯抻抩抰抸攽斨斻昉旼昄昒昈旻昃昋昍昅旽昑昐曶朊枅杬枎枒杶杻枘枆构杴枍枌杺枟枑枙枃杽极杸杹枔欥殀歾毞氝沓泬泫泮泙沶泔沭泧沷泐泂沺泃泆泭泲"],["cd40","泒泝沴沊沝沀泞泀洰泍泇沰泹泏泩泑炔炘炅炓炆炄炑炖炂炚炃牪狖狋狘狉狜狒狔狚狌狑玤玡玭玦玢玠玬玝瓝瓨甿畀甾疌疘皯盳盱盰盵矸矼矹矻矺"],["cda1","矷祂礿秅穸穻竻籵糽耵肏肮肣肸肵肭舠芠苀芫芚芘芛芵芧芮芼芞芺芴芨芡芩苂芤苃芶芢虰虯虭虮豖迒迋迓迍迖迕迗邲邴邯邳邰阹阽阼阺陃俍俅俓侲俉俋俁俔俜俙侻侳俛俇俖侺俀侹俬剄剉勀勂匽卼厗厖厙厘咺咡咭咥哏"],["ce40","哃茍咷咮哖咶哅哆咠呰咼咢咾呲哞咰垵垞垟垤垌垗垝垛垔垘垏垙垥垚垕壴复奓姡姞姮娀姱姝姺姽姼姶姤姲姷姛姩姳姵姠姾姴姭宨屌峐峘峌峗峋峛"],["cea1","峞峚峉峇峊峖峓峔峏峈峆峎峟峸巹帡帢帣帠帤庰庤庢庛庣庥弇弮彖徆怷怹恔恲恞恅恓恇恉恛恌恀恂恟怤恄恘恦恮扂扃拏挍挋拵挎挃拫拹挏挌拸拶挀挓挔拺挕拻拰敁敃斪斿昶昡昲昵昜昦昢昳昫昺昝昴昹昮朏朐柁柲柈枺"],["cf40","柜枻柸柘柀枷柅柫柤柟枵柍枳柷柶柮柣柂枹柎柧柰枲柼柆柭柌枮柦柛柺柉柊柃柪柋欨殂殄殶毖毘毠氠氡洨洴洭洟洼洿洒洊泚洳洄洙洺洚洑洀洝浂"],["cfa1","洁洘洷洃洏浀洇洠洬洈洢洉洐炷炟炾炱炰炡炴炵炩牁牉牊牬牰牳牮狊狤狨狫狟狪狦狣玅珌珂珈珅玹玶玵玴珫玿珇玾珃珆玸珋瓬瓮甮畇畈疧疪癹盄眈眃眄眅眊盷盻盺矧矨砆砑砒砅砐砏砎砉砃砓祊祌祋祅祄秕种秏秖秎窀"],["d040","穾竑笀笁籺籸籹籿粀粁紃紈紁罘羑羍羾耇耎耏耔耷胘胇胠胑胈胂胐胅胣胙胜胊胕胉胏胗胦胍臿舡芔苙苾苹茇苨茀苕茺苫苖苴苬苡苲苵茌苻苶苰苪"],["d0a1","苤苠苺苳苭虷虴虼虳衁衎衧衪衩觓訄訇赲迣迡迮迠郱邽邿郕郅邾郇郋郈釔釓陔陏陑陓陊陎倞倅倇倓倢倰倛俵俴倳倷倬俶俷倗倜倠倧倵倯倱倎党冔冓凊凄凅凈凎剡剚剒剞剟剕剢勍匎厞唦哢唗唒哧哳哤唚哿唄唈哫唑唅哱"],["d140","唊哻哷哸哠唎唃唋圁圂埌堲埕埒垺埆垽垼垸垶垿埇埐垹埁夎奊娙娖娭娮娕娏娗娊娞娳孬宧宭宬尃屖屔峬峿峮峱峷崀峹帩帨庨庮庪庬弳弰彧恝恚恧"],["d1a1","恁悢悈悀悒悁悝悃悕悛悗悇悜悎戙扆拲挐捖挬捄捅挶捃揤挹捋捊挼挩捁挴捘捔捙挭捇挳捚捑挸捗捀捈敊敆旆旃旄旂晊晟晇晑朒朓栟栚桉栲栳栻桋桏栖栱栜栵栫栭栯桎桄栴栝栒栔栦栨栮桍栺栥栠欬欯欭欱欴歭肂殈毦毤"],["d240","毨毣毢毧氥浺浣浤浶洍浡涒浘浢浭浯涑涍淯浿涆浞浧浠涗浰浼浟涂涘洯浨涋浾涀涄洖涃浻浽浵涐烜烓烑烝烋缹烢烗烒烞烠烔烍烅烆烇烚烎烡牂牸"],["d2a1","牷牶猀狺狴狾狶狳狻猁珓珙珥珖玼珧珣珩珜珒珛珔珝珚珗珘珨瓞瓟瓴瓵甡畛畟疰痁疻痄痀疿疶疺皊盉眝眛眐眓眒眣眑眕眙眚眢眧砣砬砢砵砯砨砮砫砡砩砳砪砱祔祛祏祜祓祒祑秫秬秠秮秭秪秜秞秝窆窉窅窋窌窊窇竘笐"],["d340","笄笓笅笏笈笊笎笉笒粄粑粊粌粈粍粅紞紝紑紎紘紖紓紟紒紏紌罜罡罞罠罝罛羖羒翃翂翀耖耾耹胺胲胹胵脁胻脀舁舯舥茳茭荄茙荑茥荖茿荁茦茜茢"],["d3a1","荂荎茛茪茈茼荍茖茤茠茷茯茩荇荅荌荓茞茬荋茧荈虓虒蚢蚨蚖蚍蚑蚞蚇蚗蚆蚋蚚蚅蚥蚙蚡蚧蚕蚘蚎蚝蚐蚔衃衄衭衵衶衲袀衱衿衯袃衾衴衼訒豇豗豻貤貣赶赸趵趷趶軑軓迾迵适迿迻逄迼迶郖郠郙郚郣郟郥郘郛郗郜郤酐"],["d440","酎酏釕釢釚陜陟隼飣髟鬯乿偰偪偡偞偠偓偋偝偲偈偍偁偛偊偢倕偅偟偩偫偣偤偆偀偮偳偗偑凐剫剭剬剮勖勓匭厜啵啶唼啍啐唴唪啑啢唶唵唰啒啅"],["d4a1","唌唲啥啎唹啈唭唻啀啋圊圇埻堔埢埶埜埴堀埭埽堈埸堋埳埏堇埮埣埲埥埬埡堎埼堐埧堁堌埱埩埰堍堄奜婠婘婕婧婞娸娵婭婐婟婥婬婓婤婗婃婝婒婄婛婈媎娾婍娹婌婰婩婇婑婖婂婜孲孮寁寀屙崞崋崝崚崠崌崨崍崦崥崏"],["d540","崰崒崣崟崮帾帴庱庴庹庲庳弶弸徛徖徟悊悐悆悾悰悺惓惔惏惤惙惝惈悱惛悷惊悿惃惍惀挲捥掊掂捽掽掞掭掝掗掫掎捯掇掐据掯捵掜捭掮捼掤挻掟"],["d5a1","捸掅掁掑掍捰敓旍晥晡晛晙晜晢朘桹梇梐梜桭桮梮梫楖桯梣梬梩桵桴梲梏桷梒桼桫桲梪梀桱桾梛梖梋梠梉梤桸桻梑梌梊桽欶欳欷欸殑殏殍殎殌氪淀涫涴涳湴涬淩淢涷淶淔渀淈淠淟淖涾淥淜淝淛淴淊涽淭淰涺淕淂淏淉"],["d640","淐淲淓淽淗淍淣涻烺焍烷焗烴焌烰焄烳焐烼烿焆焓焀烸烶焋焂焎牾牻牼牿猝猗猇猑猘猊猈狿猏猞玈珶珸珵琄琁珽琇琀珺珼珿琌琋珴琈畤畣痎痒痏"],["d6a1","痋痌痑痐皏皉盓眹眯眭眱眲眴眳眽眥眻眵硈硒硉硍硊硌砦硅硐祤祧祩祪祣祫祡离秺秸秶秷窏窔窐笵筇笴笥笰笢笤笳笘笪笝笱笫笭笯笲笸笚笣粔粘粖粣紵紽紸紶紺絅紬紩絁絇紾紿絊紻紨罣羕羜羝羛翊翋翍翐翑翇翏翉耟"],["d740","耞耛聇聃聈脘脥脙脛脭脟脬脞脡脕脧脝脢舑舸舳舺舴舲艴莐莣莨莍荺荳莤荴莏莁莕莙荵莔莩荽莃莌莝莛莪莋荾莥莯莈莗莰荿莦莇莮荶莚虙虖蚿蚷"],["d7a1","蛂蛁蛅蚺蚰蛈蚹蚳蚸蛌蚴蚻蚼蛃蚽蚾衒袉袕袨袢袪袚袑袡袟袘袧袙袛袗袤袬袌袓袎覂觖觙觕訰訧訬訞谹谻豜豝豽貥赽赻赹趼跂趹趿跁軘軞軝軜軗軠軡逤逋逑逜逌逡郯郪郰郴郲郳郔郫郬郩酖酘酚酓酕釬釴釱釳釸釤釹釪"],["d840","釫釷釨釮镺閆閈陼陭陫陱陯隿靪頄飥馗傛傕傔傞傋傣傃傌傎傝偨傜傒傂傇兟凔匒匑厤厧喑喨喥喭啷噅喢喓喈喏喵喁喣喒喤啽喌喦啿喕喡喎圌堩堷"],["d8a1","堙堞堧堣堨埵塈堥堜堛堳堿堶堮堹堸堭堬堻奡媯媔媟婺媢媞婸媦婼媥媬媕媮娷媄媊媗媃媋媩婻婽媌媜媏媓媝寪寍寋寔寑寊寎尌尰崷嵃嵫嵁嵋崿崵嵑嵎嵕崳崺嵒崽崱嵙嵂崹嵉崸崼崲崶嵀嵅幄幁彘徦徥徫惉悹惌惢惎惄愔"],["d940","惲愊愖愅惵愓惸惼惾惁愃愘愝愐惿愄愋扊掔掱掰揎揥揨揯揃撝揳揊揠揶揕揲揵摡揟掾揝揜揄揘揓揂揇揌揋揈揰揗揙攲敧敪敤敜敨敥斌斝斞斮旐旒"],["d9a1","晼晬晻暀晱晹晪晲朁椌棓椄棜椪棬棪棱椏棖棷棫棤棶椓椐棳棡椇棌椈楰梴椑棯棆椔棸棐棽棼棨椋椊椗棎棈棝棞棦棴棑椆棔棩椕椥棇欹欻欿欼殔殗殙殕殽毰毲毳氰淼湆湇渟湉溈渼渽湅湢渫渿湁湝湳渜渳湋湀湑渻渃渮湞"],["da40","湨湜湡渱渨湠湱湫渹渢渰湓湥渧湸湤湷湕湹湒湦渵渶湚焠焞焯烻焮焱焣焥焢焲焟焨焺焛牋牚犈犉犆犅犋猒猋猰猢猱猳猧猲猭猦猣猵猌琮琬琰琫琖"],["daa1","琚琡琭琱琤琣琝琩琠琲瓻甯畯畬痧痚痡痦痝痟痤痗皕皒盚睆睇睄睍睅睊睎睋睌矞矬硠硤硥硜硭硱硪确硰硩硨硞硢祴祳祲祰稂稊稃稌稄窙竦竤筊笻筄筈筌筎筀筘筅粢粞粨粡絘絯絣絓絖絧絪絏絭絜絫絒絔絩絑絟絎缾缿罥"],["db40","罦羢羠羡翗聑聏聐胾胔腃腊腒腏腇脽腍脺臦臮臷臸臹舄舼舽舿艵茻菏菹萣菀菨萒菧菤菼菶萐菆菈菫菣莿萁菝菥菘菿菡菋菎菖菵菉萉萏菞萑萆菂菳"],["dba1","菕菺菇菑菪萓菃菬菮菄菻菗菢萛菛菾蛘蛢蛦蛓蛣蛚蛪蛝蛫蛜蛬蛩蛗蛨蛑衈衖衕袺裗袹袸裀袾袶袼袷袽袲褁裉覕覘覗觝觚觛詎詍訹詙詀詗詘詄詅詒詈詑詊詌詏豟貁貀貺貾貰貹貵趄趀趉跘跓跍跇跖跜跏跕跙跈跗跅軯軷軺"],["dc40","軹軦軮軥軵軧軨軶軫軱軬軴軩逭逴逯鄆鄬鄄郿郼鄈郹郻鄁鄀鄇鄅鄃酡酤酟酢酠鈁鈊鈥鈃鈚鈦鈏鈌鈀鈒釿釽鈆鈄鈧鈂鈜鈤鈙鈗鈅鈖镻閍閌閐隇陾隈"],["dca1","隉隃隀雂雈雃雱雰靬靰靮頇颩飫鳦黹亃亄亶傽傿僆傮僄僊傴僈僂傰僁傺傱僋僉傶傸凗剺剸剻剼嗃嗛嗌嗐嗋嗊嗝嗀嗔嗄嗩喿嗒喍嗏嗕嗢嗖嗈嗲嗍嗙嗂圔塓塨塤塏塍塉塯塕塎塝塙塥塛堽塣塱壼嫇嫄嫋媺媸媱媵媰媿嫈媻嫆"],["dd40","媷嫀嫊媴媶嫍媹媐寖寘寙尟尳嵱嵣嵊嵥嵲嵬嵞嵨嵧嵢巰幏幎幊幍幋廅廌廆廋廇彀徯徭惷慉慊愫慅愶愲愮慆愯慏愩慀戠酨戣戥戤揅揱揫搐搒搉搠搤"],["dda1","搳摃搟搕搘搹搷搢搣搌搦搰搨摁搵搯搊搚摀搥搧搋揧搛搮搡搎敯斒旓暆暌暕暐暋暊暙暔晸朠楦楟椸楎楢楱椿楅楪椹楂楗楙楺楈楉椵楬椳椽楥棰楸椴楩楀楯楄楶楘楁楴楌椻楋椷楜楏楑椲楒椯楻椼歆歅歃歂歈歁殛嗀毻毼"],["de40","毹毷毸溛滖滈溏滀溟溓溔溠溱溹滆滒溽滁溞滉溷溰滍溦滏溲溾滃滜滘溙溒溎溍溤溡溿溳滐滊溗溮溣煇煔煒煣煠煁煝煢煲煸煪煡煂煘煃煋煰煟煐煓"],["dea1","煄煍煚牏犍犌犑犐犎猼獂猻猺獀獊獉瑄瑊瑋瑒瑑瑗瑀瑏瑐瑎瑂瑆瑍瑔瓡瓿瓾瓽甝畹畷榃痯瘏瘃痷痾痼痹痸瘐痻痶痭痵痽皙皵盝睕睟睠睒睖睚睩睧睔睙睭矠碇碚碔碏碄碕碅碆碡碃硹碙碀碖硻祼禂祽祹稑稘稙稒稗稕稢稓"],["df40","稛稐窣窢窞竫筦筤筭筴筩筲筥筳筱筰筡筸筶筣粲粴粯綈綆綀綍絿綅絺綎絻綃絼綌綔綄絽綒罭罫罧罨罬羦羥羧翛翜耡腤腠腷腜腩腛腢腲朡腞腶腧腯"],["dfa1","腄腡舝艉艄艀艂艅蓱萿葖葶葹蒏蒍葥葑葀蒆葧萰葍葽葚葙葴葳葝蔇葞萷萺萴葺葃葸萲葅萩菙葋萯葂萭葟葰萹葎葌葒葯蓅蒎萻葇萶萳葨葾葄萫葠葔葮葐蜋蜄蛷蜌蛺蛖蛵蝍蛸蜎蜉蜁蛶蜍蜅裖裋裍裎裞裛裚裌裐覅覛觟觥觤"],["e040","觡觠觢觜触詶誆詿詡訿詷誂誄詵誃誁詴詺谼豋豊豥豤豦貆貄貅賌赨赩趑趌趎趏趍趓趔趐趒跰跠跬跱跮跐跩跣跢跧跲跫跴輆軿輁輀輅輇輈輂輋遒逿"],["e0a1","遄遉逽鄐鄍鄏鄑鄖鄔鄋鄎酮酯鉈鉒鈰鈺鉦鈳鉥鉞銃鈮鉊鉆鉭鉬鉏鉠鉧鉯鈶鉡鉰鈱鉔鉣鉐鉲鉎鉓鉌鉖鈲閟閜閞閛隒隓隑隗雎雺雽雸雵靳靷靸靲頏頍頎颬飶飹馯馲馰馵骭骫魛鳪鳭鳧麀黽僦僔僗僨僳僛僪僝僤僓僬僰僯僣僠"],["e140","凘劀劁勩勫匰厬嘧嘕嘌嘒嗼嘏嘜嘁嘓嘂嗺嘝嘄嗿嗹墉塼墐墘墆墁塿塴墋塺墇墑墎塶墂墈塻墔墏壾奫嫜嫮嫥嫕嫪嫚嫭嫫嫳嫢嫠嫛嫬嫞嫝嫙嫨嫟孷寠"],["e1a1","寣屣嶂嶀嵽嶆嵺嶁嵷嶊嶉嶈嵾嵼嶍嵹嵿幘幙幓廘廑廗廎廜廕廙廒廔彄彃彯徶愬愨慁慞慱慳慒慓慲慬憀慴慔慺慛慥愻慪慡慖戩戧戫搫摍摛摝摴摶摲摳摽摵摦撦摎撂摞摜摋摓摠摐摿搿摬摫摙摥摷敳斠暡暠暟朅朄朢榱榶槉"],["e240","榠槎榖榰榬榼榑榙榎榧榍榩榾榯榿槄榽榤槔榹槊榚槏榳榓榪榡榞槙榗榐槂榵榥槆歊歍歋殞殟殠毃毄毾滎滵滱漃漥滸漷滻漮漉潎漙漚漧漘漻漒滭漊"],["e2a1","漶潳滹滮漭潀漰漼漵滫漇漎潃漅滽滶漹漜滼漺漟漍漞漈漡熇熐熉熀熅熂熏煻熆熁熗牄牓犗犕犓獃獍獑獌瑢瑳瑱瑵瑲瑧瑮甀甂甃畽疐瘖瘈瘌瘕瘑瘊瘔皸瞁睼瞅瞂睮瞀睯睾瞃碲碪碴碭碨硾碫碞碥碠碬碢碤禘禊禋禖禕禔禓"],["e340","禗禈禒禐稫穊稰稯稨稦窨窫窬竮箈箜箊箑箐箖箍箌箛箎箅箘劄箙箤箂粻粿粼粺綧綷緂綣綪緁緀緅綝緎緄緆緋緌綯綹綖綼綟綦綮綩綡緉罳翢翣翥翞"],["e3a1","耤聝聜膉膆膃膇膍膌膋舕蒗蒤蒡蒟蒺蓎蓂蒬蒮蒫蒹蒴蓁蓍蒪蒚蒱蓐蒝蒧蒻蒢蒔蓇蓌蒛蒩蒯蒨蓖蒘蒶蓏蒠蓗蓔蓒蓛蒰蒑虡蜳蜣蜨蝫蝀蜮蜞蜡蜙蜛蝃蜬蝁蜾蝆蜠蜲蜪蜭蜼蜒蜺蜱蜵蝂蜦蜧蜸蜤蜚蜰蜑裷裧裱裲裺裾裮裼裶裻"],["e440","裰裬裫覝覡覟覞觩觫觨誫誙誋誒誏誖谽豨豩賕賏賗趖踉踂跿踍跽踊踃踇踆踅跾踀踄輐輑輎輍鄣鄜鄠鄢鄟鄝鄚鄤鄡鄛酺酲酹酳銥銤鉶銛鉺銠銔銪銍"],["e4a1","銦銚銫鉹銗鉿銣鋮銎銂銕銢鉽銈銡銊銆銌銙銧鉾銇銩銝銋鈭隞隡雿靘靽靺靾鞃鞀鞂靻鞄鞁靿韎韍頖颭颮餂餀餇馝馜駃馹馻馺駂馽駇骱髣髧鬾鬿魠魡魟鳱鳲鳵麧僿儃儰僸儆儇僶僾儋儌僽儊劋劌勱勯噈噂噌嘵噁噊噉噆噘"],["e540","噚噀嘳嘽嘬嘾嘸嘪嘺圚墫墝墱墠墣墯墬墥墡壿嫿嫴嫽嫷嫶嬃嫸嬂嫹嬁嬇嬅嬏屧嶙嶗嶟嶒嶢嶓嶕嶠嶜嶡嶚嶞幩幝幠幜緳廛廞廡彉徲憋憃慹憱憰憢憉"],["e5a1","憛憓憯憭憟憒憪憡憍慦憳戭摮摰撖撠撅撗撜撏撋撊撌撣撟摨撱撘敶敺敹敻斲斳暵暰暩暲暷暪暯樀樆樗槥槸樕槱槤樠槿槬槢樛樝槾樧槲槮樔槷槧橀樈槦槻樍槼槫樉樄樘樥樏槶樦樇槴樖歑殥殣殢殦氁氀毿氂潁漦潾澇濆澒"],["e640","澍澉澌潢潏澅潚澖潶潬澂潕潲潒潐潗澔澓潝漀潡潫潽潧澐潓澋潩潿澕潣潷潪潻熲熯熛熰熠熚熩熵熝熥熞熤熡熪熜熧熳犘犚獘獒獞獟獠獝獛獡獚獙"],["e6a1","獢璇璉璊璆璁瑽璅璈瑼瑹甈甇畾瘥瘞瘙瘝瘜瘣瘚瘨瘛皜皝皞皛瞍瞏瞉瞈磍碻磏磌磑磎磔磈磃磄磉禚禡禠禜禢禛歶稹窲窴窳箷篋箾箬篎箯箹篊箵糅糈糌糋緷緛緪緧緗緡縃緺緦緶緱緰緮緟罶羬羰羭翭翫翪翬翦翨聤聧膣膟"],["e740","膞膕膢膙膗舖艏艓艒艐艎艑蔤蔻蔏蔀蔩蔎蔉蔍蔟蔊蔧蔜蓻蔫蓺蔈蔌蓴蔪蓲蔕蓷蓫蓳蓼蔒蓪蓩蔖蓾蔨蔝蔮蔂蓽蔞蓶蔱蔦蓧蓨蓰蓯蓹蔘蔠蔰蔋蔙蔯虢"],["e7a1","蝖蝣蝤蝷蟡蝳蝘蝔蝛蝒蝡蝚蝑蝞蝭蝪蝐蝎蝟蝝蝯蝬蝺蝮蝜蝥蝏蝻蝵蝢蝧蝩衚褅褌褔褋褗褘褙褆褖褑褎褉覢覤覣觭觰觬諏諆誸諓諑諔諕誻諗誾諀諅諘諃誺誽諙谾豍貏賥賟賙賨賚賝賧趠趜趡趛踠踣踥踤踮踕踛踖踑踙踦踧"],["e840","踔踒踘踓踜踗踚輬輤輘輚輠輣輖輗遳遰遯遧遫鄯鄫鄩鄪鄲鄦鄮醅醆醊醁醂醄醀鋐鋃鋄鋀鋙銶鋏鋱鋟鋘鋩鋗鋝鋌鋯鋂鋨鋊鋈鋎鋦鋍鋕鋉鋠鋞鋧鋑鋓"],["e8a1","銵鋡鋆銴镼閬閫閮閰隤隢雓霅霈霂靚鞊鞎鞈韐韏頞頝頦頩頨頠頛頧颲餈飺餑餔餖餗餕駜駍駏駓駔駎駉駖駘駋駗駌骳髬髫髳髲髱魆魃魧魴魱魦魶魵魰魨魤魬鳼鳺鳽鳿鳷鴇鴀鳹鳻鴈鴅鴄麃黓鼏鼐儜儓儗儚儑凞匴叡噰噠噮"],["e940","噳噦噣噭噲噞噷圜圛壈墽壉墿墺壂墼壆嬗嬙嬛嬡嬔嬓嬐嬖嬨嬚嬠嬞寯嶬嶱嶩嶧嶵嶰嶮嶪嶨嶲嶭嶯嶴幧幨幦幯廩廧廦廨廥彋徼憝憨憖懅憴懆懁懌憺"],["e9a1","憿憸憌擗擖擐擏擉撽撉擃擛擳擙攳敿敼斢曈暾曀曊曋曏暽暻暺曌朣樴橦橉橧樲橨樾橝橭橶橛橑樨橚樻樿橁橪橤橐橏橔橯橩橠樼橞橖橕橍橎橆歕歔歖殧殪殫毈毇氄氃氆澭濋澣濇澼濎濈潞濄澽澞濊澨瀄澥澮澺澬澪濏澿澸"],["ea40","澢濉澫濍澯澲澰燅燂熿熸燖燀燁燋燔燊燇燏熽燘熼燆燚燛犝犞獩獦獧獬獥獫獪瑿璚璠璔璒璕璡甋疀瘯瘭瘱瘽瘳瘼瘵瘲瘰皻盦瞚瞝瞡瞜瞛瞢瞣瞕瞙"],["eaa1","瞗磝磩磥磪磞磣磛磡磢磭磟磠禤穄穈穇窶窸窵窱窷篞篣篧篝篕篥篚篨篹篔篪篢篜篫篘篟糒糔糗糐糑縒縡縗縌縟縠縓縎縜縕縚縢縋縏縖縍縔縥縤罃罻罼罺羱翯耪耩聬膱膦膮膹膵膫膰膬膴膲膷膧臲艕艖艗蕖蕅蕫蕍蕓蕡蕘"],["eb40","蕀蕆蕤蕁蕢蕄蕑蕇蕣蔾蕛蕱蕎蕮蕵蕕蕧蕠薌蕦蕝蕔蕥蕬虣虥虤螛螏螗螓螒螈螁螖螘蝹螇螣螅螐螑螝螄螔螜螚螉褞褦褰褭褮褧褱褢褩褣褯褬褟觱諠"],["eba1","諢諲諴諵諝謔諤諟諰諈諞諡諨諿諯諻貑貒貐賵賮賱賰賳赬赮趥趧踳踾踸蹀蹅踶踼踽蹁踰踿躽輶輮輵輲輹輷輴遶遹遻邆郺鄳鄵鄶醓醐醑醍醏錧錞錈錟錆錏鍺錸錼錛錣錒錁鍆錭錎錍鋋錝鋺錥錓鋹鋷錴錂錤鋿錩錹錵錪錔錌"],["ec40","錋鋾錉錀鋻錖閼闍閾閹閺閶閿閵閽隩雔霋霒霐鞙鞗鞔韰韸頵頯頲餤餟餧餩馞駮駬駥駤駰駣駪駩駧骹骿骴骻髶髺髹髷鬳鮀鮅鮇魼魾魻鮂鮓鮒鮐魺鮕"],["eca1","魽鮈鴥鴗鴠鴞鴔鴩鴝鴘鴢鴐鴙鴟麈麆麇麮麭黕黖黺鼒鼽儦儥儢儤儠儩勴嚓嚌嚍嚆嚄嚃噾嚂噿嚁壖壔壏壒嬭嬥嬲嬣嬬嬧嬦嬯嬮孻寱寲嶷幬幪徾徻懃憵憼懧懠懥懤懨懞擯擩擣擫擤擨斁斀斶旚曒檍檖檁檥檉檟檛檡檞檇檓檎"],["ed40","檕檃檨檤檑橿檦檚檅檌檒歛殭氉濌澩濴濔濣濜濭濧濦濞濲濝濢濨燡燱燨燲燤燰燢獳獮獯璗璲璫璐璪璭璱璥璯甐甑甒甏疄癃癈癉癇皤盩瞵瞫瞲瞷瞶"],["eda1","瞴瞱瞨矰磳磽礂磻磼磲礅磹磾礄禫禨穜穛穖穘穔穚窾竀竁簅簏篲簀篿篻簎篴簋篳簂簉簃簁篸篽簆篰篱簐簊糨縭縼繂縳顈縸縪繉繀繇縩繌縰縻縶繄縺罅罿罾罽翴翲耬膻臄臌臊臅臇膼臩艛艚艜薃薀薏薧薕薠薋薣蕻薤薚薞"],["ee40","蕷蕼薉薡蕺蕸蕗薎薖薆薍薙薝薁薢薂薈薅蕹蕶薘薐薟虨螾螪螭蟅螰螬螹螵螼螮蟉蟃蟂蟌螷螯蟄蟊螴螶螿螸螽蟞螲褵褳褼褾襁襒褷襂覭覯覮觲觳謞"],["eea1","謘謖謑謅謋謢謏謒謕謇謍謈謆謜謓謚豏豰豲豱豯貕貔賹赯蹎蹍蹓蹐蹌蹇轃轀邅遾鄸醚醢醛醙醟醡醝醠鎡鎃鎯鍤鍖鍇鍼鍘鍜鍶鍉鍐鍑鍠鍭鎏鍌鍪鍹鍗鍕鍒鍏鍱鍷鍻鍡鍞鍣鍧鎀鍎鍙闇闀闉闃闅閷隮隰隬霠霟霘霝霙鞚鞡鞜"],["ef40","鞞鞝韕韔韱顁顄顊顉顅顃餥餫餬餪餳餲餯餭餱餰馘馣馡騂駺駴駷駹駸駶駻駽駾駼騃骾髾髽鬁髼魈鮚鮨鮞鮛鮦鮡鮥鮤鮆鮢鮠鮯鴳鵁鵧鴶鴮鴯鴱鴸鴰"],["efa1","鵅鵂鵃鴾鴷鵀鴽翵鴭麊麉麍麰黈黚黻黿鼤鼣鼢齔龠儱儭儮嚘嚜嚗嚚嚝嚙奰嬼屩屪巀幭幮懘懟懭懮懱懪懰懫懖懩擿攄擽擸攁攃擼斔旛曚曛曘櫅檹檽櫡櫆檺檶檷櫇檴檭歞毉氋瀇瀌瀍瀁瀅瀔瀎濿瀀濻瀦濼濷瀊爁燿燹爃燽獶"],["f040","璸瓀璵瓁璾璶璻瓂甔甓癜癤癙癐癓癗癚皦皽盬矂瞺磿礌礓礔礉礐礒礑禭禬穟簜簩簙簠簟簭簝簦簨簢簥簰繜繐繖繣繘繢繟繑繠繗繓羵羳翷翸聵臑臒"],["f0a1","臐艟艞薴藆藀藃藂薳薵薽藇藄薿藋藎藈藅薱薶藒蘤薸薷薾虩蟧蟦蟢蟛蟫蟪蟥蟟蟳蟤蟔蟜蟓蟭蟘蟣螤蟗蟙蠁蟴蟨蟝襓襋襏襌襆襐襑襉謪謧謣謳謰謵譇謯謼謾謱謥謷謦謶謮謤謻謽謺豂豵貙貘貗賾贄贂贀蹜蹢蹠蹗蹖蹞蹥蹧"],["f140","蹛蹚蹡蹝蹩蹔轆轇轈轋鄨鄺鄻鄾醨醥醧醯醪鎵鎌鎒鎷鎛鎝鎉鎧鎎鎪鎞鎦鎕鎈鎙鎟鎍鎱鎑鎲鎤鎨鎴鎣鎥闒闓闑隳雗雚巂雟雘雝霣霢霥鞬鞮鞨鞫鞤鞪"],["f1a1","鞢鞥韗韙韖韘韺顐顑顒颸饁餼餺騏騋騉騍騄騑騊騅騇騆髀髜鬈鬄鬅鬩鬵魊魌魋鯇鯆鯃鮿鯁鮵鮸鯓鮶鯄鮹鮽鵜鵓鵏鵊鵛鵋鵙鵖鵌鵗鵒鵔鵟鵘鵚麎麌黟鼁鼀鼖鼥鼫鼪鼩鼨齌齕儴儵劖勷厴嚫嚭嚦嚧嚪嚬壚壝壛夒嬽嬾嬿巃幰"],["f240","徿懻攇攐攍攉攌攎斄旞旝曞櫧櫠櫌櫑櫙櫋櫟櫜櫐櫫櫏櫍櫞歠殰氌瀙瀧瀠瀖瀫瀡瀢瀣瀩瀗瀤瀜瀪爌爊爇爂爅犥犦犤犣犡瓋瓅璷瓃甖癠矉矊矄矱礝礛"],["f2a1","礡礜礗礞禰穧穨簳簼簹簬簻糬糪繶繵繸繰繷繯繺繲繴繨罋罊羃羆羷翽翾聸臗臕艤艡艣藫藱藭藙藡藨藚藗藬藲藸藘藟藣藜藑藰藦藯藞藢蠀蟺蠃蟶蟷蠉蠌蠋蠆蟼蠈蟿蠊蠂襢襚襛襗襡襜襘襝襙覈覷覶觶譐譈譊譀譓譖譔譋譕"],["f340","譑譂譒譗豃豷豶貚贆贇贉趬趪趭趫蹭蹸蹳蹪蹯蹻軂轒轑轏轐轓辴酀鄿醰醭鏞鏇鏏鏂鏚鏐鏹鏬鏌鏙鎩鏦鏊鏔鏮鏣鏕鏄鏎鏀鏒鏧镽闚闛雡霩霫霬霨霦"],["f3a1","鞳鞷鞶韝韞韟顜顙顝顗颿颽颻颾饈饇饃馦馧騚騕騥騝騤騛騢騠騧騣騞騜騔髂鬋鬊鬎鬌鬷鯪鯫鯠鯞鯤鯦鯢鯰鯔鯗鯬鯜鯙鯥鯕鯡鯚鵷鶁鶊鶄鶈鵱鶀鵸鶆鶋鶌鵽鵫鵴鵵鵰鵩鶅鵳鵻鶂鵯鵹鵿鶇鵨麔麑黀黼鼭齀齁齍齖齗齘匷嚲"],["f440","嚵嚳壣孅巆巇廮廯忀忁懹攗攖攕攓旟曨曣曤櫳櫰櫪櫨櫹櫱櫮櫯瀼瀵瀯瀷瀴瀱灂瀸瀿瀺瀹灀瀻瀳灁爓爔犨獽獼璺皫皪皾盭矌矎矏矍矲礥礣礧礨礤礩"],["f4a1","禲穮穬穭竷籉籈籊籇籅糮繻繾纁纀羺翿聹臛臙舋艨艩蘢藿蘁藾蘛蘀藶蘄蘉蘅蘌藽蠙蠐蠑蠗蠓蠖襣襦覹觷譠譪譝譨譣譥譧譭趮躆躈躄轙轖轗轕轘轚邍酃酁醷醵醲醳鐋鐓鏻鐠鐏鐔鏾鐕鐐鐨鐙鐍鏵鐀鏷鐇鐎鐖鐒鏺鐉鏸鐊鏿"],["f540","鏼鐌鏶鐑鐆闞闠闟霮霯鞹鞻韽韾顠顢顣顟飁飂饐饎饙饌饋饓騲騴騱騬騪騶騩騮騸騭髇髊髆鬐鬒鬑鰋鰈鯷鰅鰒鯸鱀鰇鰎鰆鰗鰔鰉鶟鶙鶤鶝鶒鶘鶐鶛"],["f5a1","鶠鶔鶜鶪鶗鶡鶚鶢鶨鶞鶣鶿鶩鶖鶦鶧麙麛麚黥黤黧黦鼰鼮齛齠齞齝齙龑儺儹劘劗囃嚽嚾孈孇巋巏廱懽攛欂櫼欃櫸欀灃灄灊灈灉灅灆爝爚爙獾甗癪矐礭礱礯籔籓糲纊纇纈纋纆纍罍羻耰臝蘘蘪蘦蘟蘣蘜蘙蘧蘮蘡蘠蘩蘞蘥"],["f640","蠩蠝蠛蠠蠤蠜蠫衊襭襩襮襫觺譹譸譅譺譻贐贔趯躎躌轞轛轝酆酄酅醹鐿鐻鐶鐩鐽鐼鐰鐹鐪鐷鐬鑀鐱闥闤闣霵霺鞿韡顤飉飆飀饘饖騹騽驆驄驂驁騺"],["f6a1","騿髍鬕鬗鬘鬖鬺魒鰫鰝鰜鰬鰣鰨鰩鰤鰡鶷鶶鶼鷁鷇鷊鷏鶾鷅鷃鶻鶵鷎鶹鶺鶬鷈鶱鶭鷌鶳鷍鶲鹺麜黫黮黭鼛鼘鼚鼱齎齥齤龒亹囆囅囋奱孋孌巕巑廲攡攠攦攢欋欈欉氍灕灖灗灒爞爟犩獿瓘瓕瓙瓗癭皭礵禴穰穱籗籜籙籛籚"],["f740","糴糱纑罏羇臞艫蘴蘵蘳蘬蘲蘶蠬蠨蠦蠪蠥襱覿覾觻譾讄讂讆讅譿贕躕躔躚躒躐躖躗轠轢酇鑌鑐鑊鑋鑏鑇鑅鑈鑉鑆霿韣顪顩飋饔饛驎驓驔驌驏驈驊"],["f7a1","驉驒驐髐鬙鬫鬻魖魕鱆鱈鰿鱄鰹鰳鱁鰼鰷鰴鰲鰽鰶鷛鷒鷞鷚鷋鷐鷜鷑鷟鷩鷙鷘鷖鷵鷕鷝麶黰鼵鼳鼲齂齫龕龢儽劙壨壧奲孍巘蠯彏戁戃戄攩攥斖曫欑欒欏毊灛灚爢玂玁玃癰矔籧籦纕艬蘺虀蘹蘼蘱蘻蘾蠰蠲蠮蠳襶襴襳觾"],["f840","讌讎讋讈豅贙躘轤轣醼鑢鑕鑝鑗鑞韄韅頀驖驙鬞鬟鬠鱒鱘鱐鱊鱍鱋鱕鱙鱌鱎鷻鷷鷯鷣鷫鷸鷤鷶鷡鷮鷦鷲鷰鷢鷬鷴鷳鷨鷭黂黐黲黳鼆鼜鼸鼷鼶齃齏"],["f8a1","齱齰齮齯囓囍孎屭攭曭曮欓灟灡灝灠爣瓛瓥矕礸禷禶籪纗羉艭虃蠸蠷蠵衋讔讕躞躟躠躝醾醽釂鑫鑨鑩雥靆靃靇韇韥驞髕魙鱣鱧鱦鱢鱞鱠鸂鷾鸇鸃鸆鸅鸀鸁鸉鷿鷽鸄麠鼞齆齴齵齶囔攮斸欘欙欗欚灢爦犪矘矙礹籩籫糶纚"],["f940","纘纛纙臠臡虆虇虈襹襺襼襻觿讘讙躥躤躣鑮鑭鑯鑱鑳靉顲饟鱨鱮鱭鸋鸍鸐鸏鸒鸑麡黵鼉齇齸齻齺齹圞灦籯蠼趲躦釃鑴鑸鑶鑵驠鱴鱳鱱鱵鸔鸓黶鼊"],["f9a1","龤灨灥糷虪蠾蠽蠿讞貜躩軉靋顳顴飌饡馫驤驦驧鬤鸕鸗齈戇欞爧虌躨钂钀钁驩驨鬮鸙爩虋讟钃鱹麷癵驫鱺鸝灩灪麤齾齉龘碁銹裏墻恒粧嫺╔╦╗╠╬╣╚╩╝╒╤╕╞╪╡╘╧╛╓╥╖╟╫╢╙╨╜║═╭╮╰╯▓"]]');

/***/ }),

/***/ 56406:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["0","\\u0000",127],["8ea1","｡",62],["a1a1","　、。，．・：；？！゛゜´｀¨＾￣＿ヽヾゝゞ〃仝々〆〇ー―‐／＼～∥｜…‥‘’“”（）〔〕［］｛｝〈",9,"＋－±×÷＝≠＜＞≦≧∞∴♂♀°′″℃￥＄￠￡％＃＆＊＠§☆★○●◎◇"],["a2a1","◆□■△▲▽▼※〒→←↑↓〓"],["a2ba","∈∋⊆⊇⊂⊃∪∩"],["a2ca","∧∨￢⇒⇔∀∃"],["a2dc","∠⊥⌒∂∇≡≒≪≫√∽∝∵∫∬"],["a2f2","Å‰♯♭♪†‡¶"],["a2fe","◯"],["a3b0","０",9],["a3c1","Ａ",25],["a3e1","ａ",25],["a4a1","ぁ",82],["a5a1","ァ",85],["a6a1","Α",16,"Σ",6],["a6c1","α",16,"σ",6],["a7a1","А",5,"ЁЖ",25],["a7d1","а",5,"ёж",25],["a8a1","─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂"],["ada1","①",19,"Ⅰ",9],["adc0","㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡"],["addf","㍻〝〟№㏍℡㊤",4,"㈱㈲㈹㍾㍽㍼≒≡∫∮∑√⊥∠∟⊿∵∩∪"],["b0a1","亜唖娃阿哀愛挨姶逢葵茜穐悪握渥旭葦芦鯵梓圧斡扱宛姐虻飴絢綾鮎或粟袷安庵按暗案闇鞍杏以伊位依偉囲夷委威尉惟意慰易椅為畏異移維緯胃萎衣謂違遺医井亥域育郁磯一壱溢逸稲茨芋鰯允印咽員因姻引飲淫胤蔭"],["b1a1","院陰隠韻吋右宇烏羽迂雨卯鵜窺丑碓臼渦嘘唄欝蔚鰻姥厩浦瓜閏噂云運雲荏餌叡営嬰影映曳栄永泳洩瑛盈穎頴英衛詠鋭液疫益駅悦謁越閲榎厭円園堰奄宴延怨掩援沿演炎焔煙燕猿縁艶苑薗遠鉛鴛塩於汚甥凹央奥往応"],["b2a1","押旺横欧殴王翁襖鴬鴎黄岡沖荻億屋憶臆桶牡乙俺卸恩温穏音下化仮何伽価佳加可嘉夏嫁家寡科暇果架歌河火珂禍禾稼箇花苛茄荷華菓蝦課嘩貨迦過霞蚊俄峨我牙画臥芽蛾賀雅餓駕介会解回塊壊廻快怪悔恢懐戒拐改"],["b3a1","魁晦械海灰界皆絵芥蟹開階貝凱劾外咳害崖慨概涯碍蓋街該鎧骸浬馨蛙垣柿蛎鈎劃嚇各廓拡撹格核殻獲確穫覚角赫較郭閣隔革学岳楽額顎掛笠樫橿梶鰍潟割喝恰括活渇滑葛褐轄且鰹叶椛樺鞄株兜竃蒲釜鎌噛鴨栢茅萱"],["b4a1","粥刈苅瓦乾侃冠寒刊勘勧巻喚堪姦完官寛干幹患感慣憾換敢柑桓棺款歓汗漢澗潅環甘監看竿管簡緩缶翰肝艦莞観諌貫還鑑間閑関陥韓館舘丸含岸巌玩癌眼岩翫贋雁頑顔願企伎危喜器基奇嬉寄岐希幾忌揮机旗既期棋棄"],["b5a1","機帰毅気汽畿祈季稀紀徽規記貴起軌輝飢騎鬼亀偽儀妓宜戯技擬欺犠疑祇義蟻誼議掬菊鞠吉吃喫桔橘詰砧杵黍却客脚虐逆丘久仇休及吸宮弓急救朽求汲泣灸球究窮笈級糾給旧牛去居巨拒拠挙渠虚許距鋸漁禦魚亨享京"],["b6a1","供侠僑兇競共凶協匡卿叫喬境峡強彊怯恐恭挟教橋況狂狭矯胸脅興蕎郷鏡響饗驚仰凝尭暁業局曲極玉桐粁僅勤均巾錦斤欣欽琴禁禽筋緊芹菌衿襟謹近金吟銀九倶句区狗玖矩苦躯駆駈駒具愚虞喰空偶寓遇隅串櫛釧屑屈"],["b7a1","掘窟沓靴轡窪熊隈粂栗繰桑鍬勲君薫訓群軍郡卦袈祁係傾刑兄啓圭珪型契形径恵慶慧憩掲携敬景桂渓畦稽系経継繋罫茎荊蛍計詣警軽頚鶏芸迎鯨劇戟撃激隙桁傑欠決潔穴結血訣月件倹倦健兼券剣喧圏堅嫌建憲懸拳捲"],["b8a1","検権牽犬献研硯絹県肩見謙賢軒遣鍵険顕験鹸元原厳幻弦減源玄現絃舷言諺限乎個古呼固姑孤己庫弧戸故枯湖狐糊袴股胡菰虎誇跨鈷雇顧鼓五互伍午呉吾娯後御悟梧檎瑚碁語誤護醐乞鯉交佼侯候倖光公功効勾厚口向"],["b9a1","后喉坑垢好孔孝宏工巧巷幸広庚康弘恒慌抗拘控攻昂晃更杭校梗構江洪浩港溝甲皇硬稿糠紅紘絞綱耕考肯肱腔膏航荒行衡講貢購郊酵鉱砿鋼閤降項香高鴻剛劫号合壕拷濠豪轟麹克刻告国穀酷鵠黒獄漉腰甑忽惚骨狛込"],["baa1","此頃今困坤墾婚恨懇昏昆根梱混痕紺艮魂些佐叉唆嵯左差査沙瑳砂詐鎖裟坐座挫債催再最哉塞妻宰彩才採栽歳済災采犀砕砦祭斎細菜裁載際剤在材罪財冴坂阪堺榊肴咲崎埼碕鷺作削咋搾昨朔柵窄策索錯桜鮭笹匙冊刷"],["bba1","察拶撮擦札殺薩雑皐鯖捌錆鮫皿晒三傘参山惨撒散桟燦珊産算纂蚕讃賛酸餐斬暫残仕仔伺使刺司史嗣四士始姉姿子屍市師志思指支孜斯施旨枝止死氏獅祉私糸紙紫肢脂至視詞詩試誌諮資賜雌飼歯事似侍児字寺慈持時"],["bca1","次滋治爾璽痔磁示而耳自蒔辞汐鹿式識鴫竺軸宍雫七叱執失嫉室悉湿漆疾質実蔀篠偲柴芝屡蕊縞舎写射捨赦斜煮社紗者謝車遮蛇邪借勺尺杓灼爵酌釈錫若寂弱惹主取守手朱殊狩珠種腫趣酒首儒受呪寿授樹綬需囚収周"],["bda1","宗就州修愁拾洲秀秋終繍習臭舟蒐衆襲讐蹴輯週酋酬集醜什住充十従戎柔汁渋獣縦重銃叔夙宿淑祝縮粛塾熟出術述俊峻春瞬竣舜駿准循旬楯殉淳準潤盾純巡遵醇順処初所暑曙渚庶緒署書薯藷諸助叙女序徐恕鋤除傷償"],["bea1","勝匠升召哨商唱嘗奨妾娼宵将小少尚庄床廠彰承抄招掌捷昇昌昭晶松梢樟樵沼消渉湘焼焦照症省硝礁祥称章笑粧紹肖菖蒋蕉衝裳訟証詔詳象賞醤鉦鍾鐘障鞘上丈丞乗冗剰城場壌嬢常情擾条杖浄状畳穣蒸譲醸錠嘱埴飾"],["bfa1","拭植殖燭織職色触食蝕辱尻伸信侵唇娠寝審心慎振新晋森榛浸深申疹真神秦紳臣芯薪親診身辛進針震人仁刃塵壬尋甚尽腎訊迅陣靭笥諏須酢図厨逗吹垂帥推水炊睡粋翠衰遂酔錐錘随瑞髄崇嵩数枢趨雛据杉椙菅頗雀裾"],["c0a1","澄摺寸世瀬畝是凄制勢姓征性成政整星晴棲栖正清牲生盛精聖声製西誠誓請逝醒青静斉税脆隻席惜戚斥昔析石積籍績脊責赤跡蹟碩切拙接摂折設窃節説雪絶舌蝉仙先千占宣専尖川戦扇撰栓栴泉浅洗染潜煎煽旋穿箭線"],["c1a1","繊羨腺舛船薦詮賎践選遷銭銑閃鮮前善漸然全禅繕膳糎噌塑岨措曾曽楚狙疏疎礎祖租粗素組蘇訴阻遡鼠僧創双叢倉喪壮奏爽宋層匝惣想捜掃挿掻操早曹巣槍槽漕燥争痩相窓糟総綜聡草荘葬蒼藻装走送遭鎗霜騒像増憎"],["c2a1","臓蔵贈造促側則即息捉束測足速俗属賊族続卒袖其揃存孫尊損村遜他多太汰詑唾堕妥惰打柁舵楕陀駄騨体堆対耐岱帯待怠態戴替泰滞胎腿苔袋貸退逮隊黛鯛代台大第醍題鷹滝瀧卓啄宅托択拓沢濯琢託鐸濁諾茸凧蛸只"],["c3a1","叩但達辰奪脱巽竪辿棚谷狸鱈樽誰丹単嘆坦担探旦歎淡湛炭短端箪綻耽胆蛋誕鍛団壇弾断暖檀段男談値知地弛恥智池痴稚置致蜘遅馳築畜竹筑蓄逐秩窒茶嫡着中仲宙忠抽昼柱注虫衷註酎鋳駐樗瀦猪苧著貯丁兆凋喋寵"],["c4a1","帖帳庁弔張彫徴懲挑暢朝潮牒町眺聴脹腸蝶調諜超跳銚長頂鳥勅捗直朕沈珍賃鎮陳津墜椎槌追鎚痛通塚栂掴槻佃漬柘辻蔦綴鍔椿潰坪壷嬬紬爪吊釣鶴亭低停偵剃貞呈堤定帝底庭廷弟悌抵挺提梯汀碇禎程締艇訂諦蹄逓"],["c5a1","邸鄭釘鼎泥摘擢敵滴的笛適鏑溺哲徹撤轍迭鉄典填天展店添纏甜貼転顛点伝殿澱田電兎吐堵塗妬屠徒斗杜渡登菟賭途都鍍砥砺努度土奴怒倒党冬凍刀唐塔塘套宕島嶋悼投搭東桃梼棟盗淘湯涛灯燈当痘祷等答筒糖統到"],["c6a1","董蕩藤討謄豆踏逃透鐙陶頭騰闘働動同堂導憧撞洞瞳童胴萄道銅峠鴇匿得徳涜特督禿篤毒独読栃橡凸突椴届鳶苫寅酉瀞噸屯惇敦沌豚遁頓呑曇鈍奈那内乍凪薙謎灘捺鍋楢馴縄畷南楠軟難汝二尼弐迩匂賑肉虹廿日乳入"],["c7a1","如尿韮任妊忍認濡禰祢寧葱猫熱年念捻撚燃粘乃廼之埜嚢悩濃納能脳膿農覗蚤巴把播覇杷波派琶破婆罵芭馬俳廃拝排敗杯盃牌背肺輩配倍培媒梅楳煤狽買売賠陪這蝿秤矧萩伯剥博拍柏泊白箔粕舶薄迫曝漠爆縛莫駁麦"],["c8a1","函箱硲箸肇筈櫨幡肌畑畠八鉢溌発醗髪伐罰抜筏閥鳩噺塙蛤隼伴判半反叛帆搬斑板氾汎版犯班畔繁般藩販範釆煩頒飯挽晩番盤磐蕃蛮匪卑否妃庇彼悲扉批披斐比泌疲皮碑秘緋罷肥被誹費避非飛樋簸備尾微枇毘琵眉美"],["c9a1","鼻柊稗匹疋髭彦膝菱肘弼必畢筆逼桧姫媛紐百謬俵彪標氷漂瓢票表評豹廟描病秒苗錨鋲蒜蛭鰭品彬斌浜瀕貧賓頻敏瓶不付埠夫婦富冨布府怖扶敷斧普浮父符腐膚芙譜負賦赴阜附侮撫武舞葡蕪部封楓風葺蕗伏副復幅服"],["caa1","福腹複覆淵弗払沸仏物鮒分吻噴墳憤扮焚奮粉糞紛雰文聞丙併兵塀幣平弊柄並蔽閉陛米頁僻壁癖碧別瞥蔑箆偏変片篇編辺返遍便勉娩弁鞭保舗鋪圃捕歩甫補輔穂募墓慕戊暮母簿菩倣俸包呆報奉宝峰峯崩庖抱捧放方朋"],["cba1","法泡烹砲縫胞芳萌蓬蜂褒訪豊邦鋒飽鳳鵬乏亡傍剖坊妨帽忘忙房暴望某棒冒紡肪膨謀貌貿鉾防吠頬北僕卜墨撲朴牧睦穆釦勃没殆堀幌奔本翻凡盆摩磨魔麻埋妹昧枚毎哩槙幕膜枕鮪柾鱒桝亦俣又抹末沫迄侭繭麿万慢満"],["cca1","漫蔓味未魅巳箕岬密蜜湊蓑稔脈妙粍民眠務夢無牟矛霧鵡椋婿娘冥名命明盟迷銘鳴姪牝滅免棉綿緬面麺摸模茂妄孟毛猛盲網耗蒙儲木黙目杢勿餅尤戻籾貰問悶紋門匁也冶夜爺耶野弥矢厄役約薬訳躍靖柳薮鑓愉愈油癒"],["cda1","諭輸唯佑優勇友宥幽悠憂揖有柚湧涌猶猷由祐裕誘遊邑郵雄融夕予余与誉輿預傭幼妖容庸揚揺擁曜楊様洋溶熔用窯羊耀葉蓉要謡踊遥陽養慾抑欲沃浴翌翼淀羅螺裸来莱頼雷洛絡落酪乱卵嵐欄濫藍蘭覧利吏履李梨理璃"],["cea1","痢裏裡里離陸律率立葎掠略劉流溜琉留硫粒隆竜龍侶慮旅虜了亮僚両凌寮料梁涼猟療瞭稜糧良諒遼量陵領力緑倫厘林淋燐琳臨輪隣鱗麟瑠塁涙累類令伶例冷励嶺怜玲礼苓鈴隷零霊麗齢暦歴列劣烈裂廉恋憐漣煉簾練聯"],["cfa1","蓮連錬呂魯櫓炉賂路露労婁廊弄朗楼榔浪漏牢狼篭老聾蝋郎六麓禄肋録論倭和話歪賄脇惑枠鷲亙亘鰐詫藁蕨椀湾碗腕"],["d0a1","弌丐丕个丱丶丼丿乂乖乘亂亅豫亊舒弍于亞亟亠亢亰亳亶从仍仄仆仂仗仞仭仟价伉佚估佛佝佗佇佶侈侏侘佻佩佰侑佯來侖儘俔俟俎俘俛俑俚俐俤俥倚倨倔倪倥倅伜俶倡倩倬俾俯們倆偃假會偕偐偈做偖偬偸傀傚傅傴傲"],["d1a1","僉僊傳僂僖僞僥僭僣僮價僵儉儁儂儖儕儔儚儡儺儷儼儻儿兀兒兌兔兢竸兩兪兮冀冂囘册冉冏冑冓冕冖冤冦冢冩冪冫决冱冲冰况冽凅凉凛几處凩凭凰凵凾刄刋刔刎刧刪刮刳刹剏剄剋剌剞剔剪剴剩剳剿剽劍劔劒剱劈劑辨"],["d2a1","辧劬劭劼劵勁勍勗勞勣勦飭勠勳勵勸勹匆匈甸匍匐匏匕匚匣匯匱匳匸區卆卅丗卉卍凖卞卩卮夘卻卷厂厖厠厦厥厮厰厶參簒雙叟曼燮叮叨叭叺吁吽呀听吭吼吮吶吩吝呎咏呵咎呟呱呷呰咒呻咀呶咄咐咆哇咢咸咥咬哄哈咨"],["d3a1","咫哂咤咾咼哘哥哦唏唔哽哮哭哺哢唹啀啣啌售啜啅啖啗唸唳啝喙喀咯喊喟啻啾喘喞單啼喃喩喇喨嗚嗅嗟嗄嗜嗤嗔嘔嗷嘖嗾嗽嘛嗹噎噐營嘴嘶嘲嘸噫噤嘯噬噪嚆嚀嚊嚠嚔嚏嚥嚮嚶嚴囂嚼囁囃囀囈囎囑囓囗囮囹圀囿圄圉"],["d4a1","圈國圍圓團圖嗇圜圦圷圸坎圻址坏坩埀垈坡坿垉垓垠垳垤垪垰埃埆埔埒埓堊埖埣堋堙堝塲堡塢塋塰毀塒堽塹墅墹墟墫墺壞墻墸墮壅壓壑壗壙壘壥壜壤壟壯壺壹壻壼壽夂夊夐夛梦夥夬夭夲夸夾竒奕奐奎奚奘奢奠奧奬奩"],["d5a1","奸妁妝佞侫妣妲姆姨姜妍姙姚娥娟娑娜娉娚婀婬婉娵娶婢婪媚媼媾嫋嫂媽嫣嫗嫦嫩嫖嫺嫻嬌嬋嬖嬲嫐嬪嬶嬾孃孅孀孑孕孚孛孥孩孰孳孵學斈孺宀它宦宸寃寇寉寔寐寤實寢寞寥寫寰寶寳尅將專對尓尠尢尨尸尹屁屆屎屓"],["d6a1","屐屏孱屬屮乢屶屹岌岑岔妛岫岻岶岼岷峅岾峇峙峩峽峺峭嶌峪崋崕崗嵜崟崛崑崔崢崚崙崘嵌嵒嵎嵋嵬嵳嵶嶇嶄嶂嶢嶝嶬嶮嶽嶐嶷嶼巉巍巓巒巖巛巫已巵帋帚帙帑帛帶帷幄幃幀幎幗幔幟幢幤幇幵并幺麼广庠廁廂廈廐廏"],["d7a1","廖廣廝廚廛廢廡廨廩廬廱廳廰廴廸廾弃弉彝彜弋弑弖弩弭弸彁彈彌彎弯彑彖彗彙彡彭彳彷徃徂彿徊很徑徇從徙徘徠徨徭徼忖忻忤忸忱忝悳忿怡恠怙怐怩怎怱怛怕怫怦怏怺恚恁恪恷恟恊恆恍恣恃恤恂恬恫恙悁悍惧悃悚"],["d8a1","悄悛悖悗悒悧悋惡悸惠惓悴忰悽惆悵惘慍愕愆惶惷愀惴惺愃愡惻惱愍愎慇愾愨愧慊愿愼愬愴愽慂慄慳慷慘慙慚慫慴慯慥慱慟慝慓慵憙憖憇憬憔憚憊憑憫憮懌懊應懷懈懃懆憺懋罹懍懦懣懶懺懴懿懽懼懾戀戈戉戍戌戔戛"],["d9a1","戞戡截戮戰戲戳扁扎扞扣扛扠扨扼抂抉找抒抓抖拔抃抔拗拑抻拏拿拆擔拈拜拌拊拂拇抛拉挌拮拱挧挂挈拯拵捐挾捍搜捏掖掎掀掫捶掣掏掉掟掵捫捩掾揩揀揆揣揉插揶揄搖搴搆搓搦搶攝搗搨搏摧摯摶摎攪撕撓撥撩撈撼"],["daa1","據擒擅擇撻擘擂擱擧舉擠擡抬擣擯攬擶擴擲擺攀擽攘攜攅攤攣攫攴攵攷收攸畋效敖敕敍敘敞敝敲數斂斃變斛斟斫斷旃旆旁旄旌旒旛旙无旡旱杲昊昃旻杳昵昶昴昜晏晄晉晁晞晝晤晧晨晟晢晰暃暈暎暉暄暘暝曁暹曉暾暼"],["dba1","曄暸曖曚曠昿曦曩曰曵曷朏朖朞朦朧霸朮朿朶杁朸朷杆杞杠杙杣杤枉杰枩杼杪枌枋枦枡枅枷柯枴柬枳柩枸柤柞柝柢柮枹柎柆柧檜栞框栩桀桍栲桎梳栫桙档桷桿梟梏梭梔條梛梃檮梹桴梵梠梺椏梍桾椁棊椈棘椢椦棡椌棍"],["dca1","棔棧棕椶椒椄棗棣椥棹棠棯椨椪椚椣椡棆楹楷楜楸楫楔楾楮椹楴椽楙椰楡楞楝榁楪榲榮槐榿槁槓榾槎寨槊槝榻槃榧樮榑榠榜榕榴槞槨樂樛槿權槹槲槧樅榱樞槭樔槫樊樒櫁樣樓橄樌橲樶橸橇橢橙橦橈樸樢檐檍檠檄檢檣"],["dda1","檗蘗檻櫃櫂檸檳檬櫞櫑櫟檪櫚櫪櫻欅蘖櫺欒欖鬱欟欸欷盜欹飮歇歃歉歐歙歔歛歟歡歸歹歿殀殄殃殍殘殕殞殤殪殫殯殲殱殳殷殼毆毋毓毟毬毫毳毯麾氈氓气氛氤氣汞汕汢汪沂沍沚沁沛汾汨汳沒沐泄泱泓沽泗泅泝沮沱沾"],["dea1","沺泛泯泙泪洟衍洶洫洽洸洙洵洳洒洌浣涓浤浚浹浙涎涕濤涅淹渕渊涵淇淦涸淆淬淞淌淨淒淅淺淙淤淕淪淮渭湮渮渙湲湟渾渣湫渫湶湍渟湃渺湎渤滿渝游溂溪溘滉溷滓溽溯滄溲滔滕溏溥滂溟潁漑灌滬滸滾漿滲漱滯漲滌"],["dfa1","漾漓滷澆潺潸澁澀潯潛濳潭澂潼潘澎澑濂潦澳澣澡澤澹濆澪濟濕濬濔濘濱濮濛瀉瀋濺瀑瀁瀏濾瀛瀚潴瀝瀘瀟瀰瀾瀲灑灣炙炒炯烱炬炸炳炮烟烋烝烙焉烽焜焙煥煕熈煦煢煌煖煬熏燻熄熕熨熬燗熹熾燒燉燔燎燠燬燧燵燼"],["e0a1","燹燿爍爐爛爨爭爬爰爲爻爼爿牀牆牋牘牴牾犂犁犇犒犖犢犧犹犲狃狆狄狎狒狢狠狡狹狷倏猗猊猜猖猝猴猯猩猥猾獎獏默獗獪獨獰獸獵獻獺珈玳珎玻珀珥珮珞璢琅瑯琥珸琲琺瑕琿瑟瑙瑁瑜瑩瑰瑣瑪瑶瑾璋璞璧瓊瓏瓔珱"],["e1a1","瓠瓣瓧瓩瓮瓲瓰瓱瓸瓷甄甃甅甌甎甍甕甓甞甦甬甼畄畍畊畉畛畆畚畩畤畧畫畭畸當疆疇畴疊疉疂疔疚疝疥疣痂疳痃疵疽疸疼疱痍痊痒痙痣痞痾痿痼瘁痰痺痲痳瘋瘍瘉瘟瘧瘠瘡瘢瘤瘴瘰瘻癇癈癆癜癘癡癢癨癩癪癧癬癰"],["e2a1","癲癶癸發皀皃皈皋皎皖皓皙皚皰皴皸皹皺盂盍盖盒盞盡盥盧盪蘯盻眈眇眄眩眤眞眥眦眛眷眸睇睚睨睫睛睥睿睾睹瞎瞋瞑瞠瞞瞰瞶瞹瞿瞼瞽瞻矇矍矗矚矜矣矮矼砌砒礦砠礪硅碎硴碆硼碚碌碣碵碪碯磑磆磋磔碾碼磅磊磬"],["e3a1","磧磚磽磴礇礒礑礙礬礫祀祠祗祟祚祕祓祺祿禊禝禧齋禪禮禳禹禺秉秕秧秬秡秣稈稍稘稙稠稟禀稱稻稾稷穃穗穉穡穢穩龝穰穹穽窈窗窕窘窖窩竈窰窶竅竄窿邃竇竊竍竏竕竓站竚竝竡竢竦竭竰笂笏笊笆笳笘笙笞笵笨笶筐"],["e4a1","筺笄筍笋筌筅筵筥筴筧筰筱筬筮箝箘箟箍箜箚箋箒箏筝箙篋篁篌篏箴篆篝篩簑簔篦篥籠簀簇簓篳篷簗簍篶簣簧簪簟簷簫簽籌籃籔籏籀籐籘籟籤籖籥籬籵粃粐粤粭粢粫粡粨粳粲粱粮粹粽糀糅糂糘糒糜糢鬻糯糲糴糶糺紆"],["e5a1","紂紜紕紊絅絋紮紲紿紵絆絳絖絎絲絨絮絏絣經綉絛綏絽綛綺綮綣綵緇綽綫總綢綯緜綸綟綰緘緝緤緞緻緲緡縅縊縣縡縒縱縟縉縋縢繆繦縻縵縹繃縷縲縺繧繝繖繞繙繚繹繪繩繼繻纃緕繽辮繿纈纉續纒纐纓纔纖纎纛纜缸缺"],["e6a1","罅罌罍罎罐网罕罔罘罟罠罨罩罧罸羂羆羃羈羇羌羔羞羝羚羣羯羲羹羮羶羸譱翅翆翊翕翔翡翦翩翳翹飜耆耄耋耒耘耙耜耡耨耿耻聊聆聒聘聚聟聢聨聳聲聰聶聹聽聿肄肆肅肛肓肚肭冐肬胛胥胙胝胄胚胖脉胯胱脛脩脣脯腋"],["e7a1","隋腆脾腓腑胼腱腮腥腦腴膃膈膊膀膂膠膕膤膣腟膓膩膰膵膾膸膽臀臂膺臉臍臑臙臘臈臚臟臠臧臺臻臾舁舂舅與舊舍舐舖舩舫舸舳艀艙艘艝艚艟艤艢艨艪艫舮艱艷艸艾芍芒芫芟芻芬苡苣苟苒苴苳苺莓范苻苹苞茆苜茉苙"],["e8a1","茵茴茖茲茱荀茹荐荅茯茫茗茘莅莚莪莟莢莖茣莎莇莊荼莵荳荵莠莉莨菴萓菫菎菽萃菘萋菁菷萇菠菲萍萢萠莽萸蔆菻葭萪萼蕚蒄葷葫蒭葮蒂葩葆萬葯葹萵蓊葢蒹蒿蒟蓙蓍蒻蓚蓐蓁蓆蓖蒡蔡蓿蓴蔗蔘蔬蔟蔕蔔蓼蕀蕣蕘蕈"],["e9a1","蕁蘂蕋蕕薀薤薈薑薊薨蕭薔薛藪薇薜蕷蕾薐藉薺藏薹藐藕藝藥藜藹蘊蘓蘋藾藺蘆蘢蘚蘰蘿虍乕虔號虧虱蚓蚣蚩蚪蚋蚌蚶蚯蛄蛆蚰蛉蠣蚫蛔蛞蛩蛬蛟蛛蛯蜒蜆蜈蜀蜃蛻蜑蜉蜍蛹蜊蜴蜿蜷蜻蜥蜩蜚蝠蝟蝸蝌蝎蝴蝗蝨蝮蝙"],["eaa1","蝓蝣蝪蠅螢螟螂螯蟋螽蟀蟐雖螫蟄螳蟇蟆螻蟯蟲蟠蠏蠍蟾蟶蟷蠎蟒蠑蠖蠕蠢蠡蠱蠶蠹蠧蠻衄衂衒衙衞衢衫袁衾袞衵衽袵衲袂袗袒袮袙袢袍袤袰袿袱裃裄裔裘裙裝裹褂裼裴裨裲褄褌褊褓襃褞褥褪褫襁襄褻褶褸襌褝襠襞"],["eba1","襦襤襭襪襯襴襷襾覃覈覊覓覘覡覩覦覬覯覲覺覽覿觀觚觜觝觧觴觸訃訖訐訌訛訝訥訶詁詛詒詆詈詼詭詬詢誅誂誄誨誡誑誥誦誚誣諄諍諂諚諫諳諧諤諱謔諠諢諷諞諛謌謇謚諡謖謐謗謠謳鞫謦謫謾謨譁譌譏譎證譖譛譚譫"],["eca1","譟譬譯譴譽讀讌讎讒讓讖讙讚谺豁谿豈豌豎豐豕豢豬豸豺貂貉貅貊貍貎貔豼貘戝貭貪貽貲貳貮貶賈賁賤賣賚賽賺賻贄贅贊贇贏贍贐齎贓賍贔贖赧赭赱赳趁趙跂趾趺跏跚跖跌跛跋跪跫跟跣跼踈踉跿踝踞踐踟蹂踵踰踴蹊"],["eda1","蹇蹉蹌蹐蹈蹙蹤蹠踪蹣蹕蹶蹲蹼躁躇躅躄躋躊躓躑躔躙躪躡躬躰軆躱躾軅軈軋軛軣軼軻軫軾輊輅輕輒輙輓輜輟輛輌輦輳輻輹轅轂輾轌轉轆轎轗轜轢轣轤辜辟辣辭辯辷迚迥迢迪迯邇迴逅迹迺逑逕逡逍逞逖逋逧逶逵逹迸"],["eea1","遏遐遑遒逎遉逾遖遘遞遨遯遶隨遲邂遽邁邀邊邉邏邨邯邱邵郢郤扈郛鄂鄒鄙鄲鄰酊酖酘酣酥酩酳酲醋醉醂醢醫醯醪醵醴醺釀釁釉釋釐釖釟釡釛釼釵釶鈞釿鈔鈬鈕鈑鉞鉗鉅鉉鉤鉈銕鈿鉋鉐銜銖銓銛鉚鋏銹銷鋩錏鋺鍄錮"],["efa1","錙錢錚錣錺錵錻鍜鍠鍼鍮鍖鎰鎬鎭鎔鎹鏖鏗鏨鏥鏘鏃鏝鏐鏈鏤鐚鐔鐓鐃鐇鐐鐶鐫鐵鐡鐺鑁鑒鑄鑛鑠鑢鑞鑪鈩鑰鑵鑷鑽鑚鑼鑾钁鑿閂閇閊閔閖閘閙閠閨閧閭閼閻閹閾闊濶闃闍闌闕闔闖關闡闥闢阡阨阮阯陂陌陏陋陷陜陞"],["f0a1","陝陟陦陲陬隍隘隕隗險隧隱隲隰隴隶隸隹雎雋雉雍襍雜霍雕雹霄霆霈霓霎霑霏霖霙霤霪霰霹霽霾靄靆靈靂靉靜靠靤靦靨勒靫靱靹鞅靼鞁靺鞆鞋鞏鞐鞜鞨鞦鞣鞳鞴韃韆韈韋韜韭齏韲竟韶韵頏頌頸頤頡頷頽顆顏顋顫顯顰"],["f1a1","顱顴顳颪颯颱颶飄飃飆飩飫餃餉餒餔餘餡餝餞餤餠餬餮餽餾饂饉饅饐饋饑饒饌饕馗馘馥馭馮馼駟駛駝駘駑駭駮駱駲駻駸騁騏騅駢騙騫騷驅驂驀驃騾驕驍驛驗驟驢驥驤驩驫驪骭骰骼髀髏髑髓體髞髟髢髣髦髯髫髮髴髱髷"],["f2a1","髻鬆鬘鬚鬟鬢鬣鬥鬧鬨鬩鬪鬮鬯鬲魄魃魏魍魎魑魘魴鮓鮃鮑鮖鮗鮟鮠鮨鮴鯀鯊鮹鯆鯏鯑鯒鯣鯢鯤鯔鯡鰺鯲鯱鯰鰕鰔鰉鰓鰌鰆鰈鰒鰊鰄鰮鰛鰥鰤鰡鰰鱇鰲鱆鰾鱚鱠鱧鱶鱸鳧鳬鳰鴉鴈鳫鴃鴆鴪鴦鶯鴣鴟鵄鴕鴒鵁鴿鴾鵆鵈"],["f3a1","鵝鵞鵤鵑鵐鵙鵲鶉鶇鶫鵯鵺鶚鶤鶩鶲鷄鷁鶻鶸鶺鷆鷏鷂鷙鷓鷸鷦鷭鷯鷽鸚鸛鸞鹵鹹鹽麁麈麋麌麒麕麑麝麥麩麸麪麭靡黌黎黏黐黔黜點黝黠黥黨黯黴黶黷黹黻黼黽鼇鼈皷鼕鼡鼬鼾齊齒齔齣齟齠齡齦齧齬齪齷齲齶龕龜龠"],["f4a1","堯槇遙瑤凜熙"],["f9a1","纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德"],["faa1","忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱"],["fba1","犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚"],["fca1","釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑"],["fcf1","ⅰ",9,"￢￤＇＂"],["8fa2af","˘ˇ¸˙˝¯˛˚～΄΅"],["8fa2c2","¡¦¿"],["8fa2eb","ºª©®™¤№"],["8fa6e1","ΆΈΉΊΪ"],["8fa6e7","Ό"],["8fa6e9","ΎΫ"],["8fa6ec","Ώ"],["8fa6f1","άέήίϊΐόςύϋΰώ"],["8fa7c2","Ђ",10,"ЎЏ"],["8fa7f2","ђ",10,"ўџ"],["8fa9a1","ÆĐ"],["8fa9a4","Ħ"],["8fa9a6","Ĳ"],["8fa9a8","ŁĿ"],["8fa9ab","ŊØŒ"],["8fa9af","ŦÞ"],["8fa9c1","æđðħıĳĸłŀŉŋøœßŧþ"],["8faaa1","ÁÀÄÂĂǍĀĄÅÃĆĈČÇĊĎÉÈËÊĚĖĒĘ"],["8faaba","ĜĞĢĠĤÍÌÏÎǏİĪĮĨĴĶĹĽĻŃŇŅÑÓÒÖÔǑŐŌÕŔŘŖŚŜŠŞŤŢÚÙÜÛŬǓŰŪŲŮŨǗǛǙǕŴÝŸŶŹŽŻ"],["8faba1","áàäâăǎāąåãćĉčçċďéèëêěėēęǵĝğ"],["8fabbd","ġĥíìïîǐ"],["8fabc5","īįĩĵķĺľļńňņñóòöôǒőōõŕřŗśŝšşťţúùüûŭǔűūųůũǘǜǚǖŵýÿŷźžż"],["8fb0a1","丂丄丅丌丒丟丣两丨丫丮丯丰丵乀乁乄乇乑乚乜乣乨乩乴乵乹乿亍亖亗亝亯亹仃仐仚仛仠仡仢仨仯仱仳仵份仾仿伀伂伃伈伋伌伒伕伖众伙伮伱你伳伵伷伹伻伾佀佂佈佉佋佌佒佔佖佘佟佣佪佬佮佱佷佸佹佺佽佾侁侂侄"],["8fb1a1","侅侉侊侌侎侐侒侓侔侗侙侚侞侟侲侷侹侻侼侽侾俀俁俅俆俈俉俋俌俍俏俒俜俠俢俰俲俼俽俿倀倁倄倇倊倌倎倐倓倗倘倛倜倝倞倢倧倮倰倲倳倵偀偁偂偅偆偊偌偎偑偒偓偗偙偟偠偢偣偦偧偪偭偰偱倻傁傃傄傆傊傎傏傐"],["8fb2a1","傒傓傔傖傛傜傞",4,"傪傯傰傹傺傽僀僃僄僇僌僎僐僓僔僘僜僝僟僢僤僦僨僩僯僱僶僺僾儃儆儇儈儋儌儍儎僲儐儗儙儛儜儝儞儣儧儨儬儭儯儱儳儴儵儸儹兂兊兏兓兕兗兘兟兤兦兾冃冄冋冎冘冝冡冣冭冸冺冼冾冿凂"],["8fb3a1","凈减凑凒凓凕凘凞凢凥凮凲凳凴凷刁刂刅划刓刕刖刘刢刨刱刲刵刼剅剉剕剗剘剚剜剟剠剡剦剮剷剸剹劀劂劅劊劌劓劕劖劗劘劚劜劤劥劦劧劯劰劶劷劸劺劻劽勀勄勆勈勌勏勑勔勖勛勜勡勥勨勩勪勬勰勱勴勶勷匀匃匊匋"],["8fb4a1","匌匑匓匘匛匜匞匟匥匧匨匩匫匬匭匰匲匵匼匽匾卂卌卋卙卛卡卣卥卬卭卲卹卾厃厇厈厎厓厔厙厝厡厤厪厫厯厲厴厵厷厸厺厽叀叅叏叒叓叕叚叝叞叠另叧叵吂吓吚吡吧吨吪启吱吴吵呃呄呇呍呏呞呢呤呦呧呩呫呭呮呴呿"],["8fb5a1","咁咃咅咈咉咍咑咕咖咜咟咡咦咧咩咪咭咮咱咷咹咺咻咿哆哊响哎哠哪哬哯哶哼哾哿唀唁唅唈唉唌唍唎唕唪唫唲唵唶唻唼唽啁啇啉啊啍啐啑啘啚啛啞啠啡啤啦啿喁喂喆喈喎喏喑喒喓喔喗喣喤喭喲喿嗁嗃嗆嗉嗋嗌嗎嗑嗒"],["8fb6a1","嗓嗗嗘嗛嗞嗢嗩嗶嗿嘅嘈嘊嘍",5,"嘙嘬嘰嘳嘵嘷嘹嘻嘼嘽嘿噀噁噃噄噆噉噋噍噏噔噞噠噡噢噣噦噩噭噯噱噲噵嚄嚅嚈嚋嚌嚕嚙嚚嚝嚞嚟嚦嚧嚨嚩嚫嚬嚭嚱嚳嚷嚾囅囉囊囋囏囐囌囍囙囜囝囟囡囤",4,"囱囫园"],["8fb7a1","囶囷圁圂圇圊圌圑圕圚圛圝圠圢圣圤圥圩圪圬圮圯圳圴圽圾圿坅坆坌坍坒坢坥坧坨坫坭",4,"坳坴坵坷坹坺坻坼坾垁垃垌垔垗垙垚垜垝垞垟垡垕垧垨垩垬垸垽埇埈埌埏埕埝埞埤埦埧埩埭埰埵埶埸埽埾埿堃堄堈堉埡"],["8fb8a1","堌堍堛堞堟堠堦堧堭堲堹堿塉塌塍塏塐塕塟塡塤塧塨塸塼塿墀墁墇墈墉墊墌墍墏墐墔墖墝墠墡墢墦墩墱墲壄墼壂壈壍壎壐壒壔壖壚壝壡壢壩壳夅夆夋夌夒夓夔虁夝夡夣夤夨夯夰夳夵夶夿奃奆奒奓奙奛奝奞奟奡奣奫奭"],["8fb9a1","奯奲奵奶她奻奼妋妌妎妒妕妗妟妤妧妭妮妯妰妳妷妺妼姁姃姄姈姊姍姒姝姞姟姣姤姧姮姯姱姲姴姷娀娄娌娍娎娒娓娞娣娤娧娨娪娭娰婄婅婇婈婌婐婕婞婣婥婧婭婷婺婻婾媋媐媓媖媙媜媞媟媠媢媧媬媱媲媳媵媸媺媻媿"],["8fbaa1","嫄嫆嫈嫏嫚嫜嫠嫥嫪嫮嫵嫶嫽嬀嬁嬈嬗嬴嬙嬛嬝嬡嬥嬭嬸孁孋孌孒孖孞孨孮孯孼孽孾孿宁宄宆宊宎宐宑宓宔宖宨宩宬宭宯宱宲宷宺宼寀寁寍寏寖",4,"寠寯寱寴寽尌尗尞尟尣尦尩尫尬尮尰尲尵尶屙屚屜屢屣屧屨屩"],["8fbba1","屭屰屴屵屺屻屼屽岇岈岊岏岒岝岟岠岢岣岦岪岲岴岵岺峉峋峒峝峗峮峱峲峴崁崆崍崒崫崣崤崦崧崱崴崹崽崿嵂嵃嵆嵈嵕嵑嵙嵊嵟嵠嵡嵢嵤嵪嵭嵰嵹嵺嵾嵿嶁嶃嶈嶊嶒嶓嶔嶕嶙嶛嶟嶠嶧嶫嶰嶴嶸嶹巃巇巋巐巎巘巙巠巤"],["8fbca1","巩巸巹帀帇帍帒帔帕帘帟帠帮帨帲帵帾幋幐幉幑幖幘幛幜幞幨幪",4,"幰庀庋庎庢庤庥庨庪庬庱庳庽庾庿廆廌廋廎廑廒廔廕廜廞廥廫异弆弇弈弎弙弜弝弡弢弣弤弨弫弬弮弰弴弶弻弽弿彀彄彅彇彍彐彔彘彛彠彣彤彧"],["8fbda1","彯彲彴彵彸彺彽彾徉徍徏徖徜徝徢徧徫徤徬徯徰徱徸忄忇忈忉忋忐",4,"忞忡忢忨忩忪忬忭忮忯忲忳忶忺忼怇怊怍怓怔怗怘怚怟怤怭怳怵恀恇恈恉恌恑恔恖恗恝恡恧恱恾恿悂悆悈悊悎悑悓悕悘悝悞悢悤悥您悰悱悷"],["8fbea1","悻悾惂惄惈惉惊惋惎惏惔惕惙惛惝惞惢惥惲惵惸惼惽愂愇愊愌愐",4,"愖愗愙愜愞愢愪愫愰愱愵愶愷愹慁慅慆慉慞慠慬慲慸慻慼慿憀憁憃憄憋憍憒憓憗憘憜憝憟憠憥憨憪憭憸憹憼懀懁懂懎懏懕懜懝懞懟懡懢懧懩懥"],["8fbfa1","懬懭懯戁戃戄戇戓戕戜戠戢戣戧戩戫戹戽扂扃扄扆扌扐扑扒扔扖扚扜扤扭扯扳扺扽抍抎抏抐抦抨抳抶抷抺抾抿拄拎拕拖拚拪拲拴拼拽挃挄挊挋挍挐挓挖挘挩挪挭挵挶挹挼捁捂捃捄捆捊捋捎捒捓捔捘捛捥捦捬捭捱捴捵"],["8fc0a1","捸捼捽捿掂掄掇掊掐掔掕掙掚掞掤掦掭掮掯掽揁揅揈揎揑揓揔揕揜揠揥揪揬揲揳揵揸揹搉搊搐搒搔搘搞搠搢搤搥搩搪搯搰搵搽搿摋摏摑摒摓摔摚摛摜摝摟摠摡摣摭摳摴摻摽撅撇撏撐撑撘撙撛撝撟撡撣撦撨撬撳撽撾撿"],["8fc1a1","擄擉擊擋擌擎擐擑擕擗擤擥擩擪擭擰擵擷擻擿攁攄攈攉攊攏攓攔攖攙攛攞攟攢攦攩攮攱攺攼攽敃敇敉敐敒敔敟敠敧敫敺敽斁斅斊斒斕斘斝斠斣斦斮斲斳斴斿旂旈旉旎旐旔旖旘旟旰旲旴旵旹旾旿昀昄昈昉昍昑昒昕昖昝"],["8fc2a1","昞昡昢昣昤昦昩昪昫昬昮昰昱昳昹昷晀晅晆晊晌晑晎晗晘晙晛晜晠晡曻晪晫晬晾晳晵晿晷晸晹晻暀晼暋暌暍暐暒暙暚暛暜暟暠暤暭暱暲暵暻暿曀曂曃曈曌曎曏曔曛曟曨曫曬曮曺朅朇朎朓朙朜朠朢朳朾杅杇杈杌杔杕杝"],["8fc3a1","杦杬杮杴杶杻极构枎枏枑枓枖枘枙枛枰枱枲枵枻枼枽柹柀柂柃柅柈柉柒柗柙柜柡柦柰柲柶柷桒栔栙栝栟栨栧栬栭栯栰栱栳栻栿桄桅桊桌桕桗桘桛桫桮",4,"桵桹桺桻桼梂梄梆梈梖梘梚梜梡梣梥梩梪梮梲梻棅棈棌棏"],["8fc4a1","棐棑棓棖棙棜棝棥棨棪棫棬棭棰棱棵棶棻棼棽椆椉椊椐椑椓椖椗椱椳椵椸椻楂楅楉楎楗楛楣楤楥楦楨楩楬楰楱楲楺楻楿榀榍榒榖榘榡榥榦榨榫榭榯榷榸榺榼槅槈槑槖槗槢槥槮槯槱槳槵槾樀樁樃樏樑樕樚樝樠樤樨樰樲"],["8fc5a1","樴樷樻樾樿橅橆橉橊橎橐橑橒橕橖橛橤橧橪橱橳橾檁檃檆檇檉檋檑檛檝檞檟檥檫檯檰檱檴檽檾檿櫆櫉櫈櫌櫐櫔櫕櫖櫜櫝櫤櫧櫬櫰櫱櫲櫼櫽欂欃欆欇欉欏欐欑欗欛欞欤欨欫欬欯欵欶欻欿歆歊歍歒歖歘歝歠歧歫歮歰歵歽"],["8fc6a1","歾殂殅殗殛殟殠殢殣殨殩殬殭殮殰殸殹殽殾毃毄毉毌毖毚毡毣毦毧毮毱毷毹毿氂氄氅氉氍氎氐氒氙氟氦氧氨氬氮氳氵氶氺氻氿汊汋汍汏汒汔汙汛汜汫汭汯汴汶汸汹汻沅沆沇沉沔沕沗沘沜沟沰沲沴泂泆泍泏泐泑泒泔泖"],["8fc7a1","泚泜泠泧泩泫泬泮泲泴洄洇洊洎洏洑洓洚洦洧洨汧洮洯洱洹洼洿浗浞浟浡浥浧浯浰浼涂涇涑涒涔涖涗涘涪涬涴涷涹涽涿淄淈淊淎淏淖淛淝淟淠淢淥淩淯淰淴淶淼渀渄渞渢渧渲渶渹渻渼湄湅湈湉湋湏湑湒湓湔湗湜湝湞"],["8fc8a1","湢湣湨湳湻湽溍溓溙溠溧溭溮溱溳溻溿滀滁滃滇滈滊滍滎滏滫滭滮滹滻滽漄漈漊漌漍漖漘漚漛漦漩漪漯漰漳漶漻漼漭潏潑潒潓潗潙潚潝潞潡潢潨潬潽潾澃澇澈澋澌澍澐澒澓澔澖澚澟澠澥澦澧澨澮澯澰澵澶澼濅濇濈濊"],["8fc9a1","濚濞濨濩濰濵濹濼濽瀀瀅瀆瀇瀍瀗瀠瀣瀯瀴瀷瀹瀼灃灄灈灉灊灋灔灕灝灞灎灤灥灬灮灵灶灾炁炅炆炔",4,"炛炤炫炰炱炴炷烊烑烓烔烕烖烘烜烤烺焃",4,"焋焌焏焞焠焫焭焯焰焱焸煁煅煆煇煊煋煐煒煗煚煜煞煠"],["8fcaa1","煨煹熀熅熇熌熒熚熛熠熢熯熰熲熳熺熿燀燁燄燋燌燓燖燙燚燜燸燾爀爇爈爉爓爗爚爝爟爤爫爯爴爸爹牁牂牃牅牎牏牐牓牕牖牚牜牞牠牣牨牫牮牯牱牷牸牻牼牿犄犉犍犎犓犛犨犭犮犱犴犾狁狇狉狌狕狖狘狟狥狳狴狺狻"],["8fcba1","狾猂猄猅猇猋猍猒猓猘猙猞猢猤猧猨猬猱猲猵猺猻猽獃獍獐獒獖獘獝獞獟獠獦獧獩獫獬獮獯獱獷獹獼玀玁玃玅玆玎玐玓玕玗玘玜玞玟玠玢玥玦玪玫玭玵玷玹玼玽玿珅珆珉珋珌珏珒珓珖珙珝珡珣珦珧珩珴珵珷珹珺珻珽"],["8fcca1","珿琀琁琄琇琊琑琚琛琤琦琨",9,"琹瑀瑃瑄瑆瑇瑋瑍瑑瑒瑗瑝瑢瑦瑧瑨瑫瑭瑮瑱瑲璀璁璅璆璇璉璏璐璑璒璘璙璚璜璟璠璡璣璦璨璩璪璫璮璯璱璲璵璹璻璿瓈瓉瓌瓐瓓瓘瓚瓛瓞瓟瓤瓨瓪瓫瓯瓴瓺瓻瓼瓿甆"],["8fcda1","甒甖甗甠甡甤甧甩甪甯甶甹甽甾甿畀畃畇畈畎畐畒畗畞畟畡畯畱畹",5,"疁疅疐疒疓疕疙疜疢疤疴疺疿痀痁痄痆痌痎痏痗痜痟痠痡痤痧痬痮痯痱痹瘀瘂瘃瘄瘇瘈瘊瘌瘏瘒瘓瘕瘖瘙瘛瘜瘝瘞瘣瘥瘦瘩瘭瘲瘳瘵瘸瘹"],["8fcea1","瘺瘼癊癀癁癃癄癅癉癋癕癙癟癤癥癭癮癯癱癴皁皅皌皍皕皛皜皝皟皠皢",6,"皪皭皽盁盅盉盋盌盎盔盙盠盦盨盬盰盱盶盹盼眀眆眊眎眒眔眕眗眙眚眜眢眨眭眮眯眴眵眶眹眽眾睂睅睆睊睍睎睏睒睖睗睜睞睟睠睢"],["8fcfa1","睤睧睪睬睰睲睳睴睺睽瞀瞄瞌瞍瞔瞕瞖瞚瞟瞢瞧瞪瞮瞯瞱瞵瞾矃矉矑矒矕矙矞矟矠矤矦矪矬矰矱矴矸矻砅砆砉砍砎砑砝砡砢砣砭砮砰砵砷硃硄硇硈硌硎硒硜硞硠硡硣硤硨硪确硺硾碊碏碔碘碡碝碞碟碤碨碬碭碰碱碲碳"],["8fd0a1","碻碽碿磇磈磉磌磎磒磓磕磖磤磛磟磠磡磦磪磲磳礀磶磷磺磻磿礆礌礐礚礜礞礟礠礥礧礩礭礱礴礵礻礽礿祄祅祆祊祋祏祑祔祘祛祜祧祩祫祲祹祻祼祾禋禌禑禓禔禕禖禘禛禜禡禨禩禫禯禱禴禸离秂秄秇秈秊秏秔秖秚秝秞"],["8fd1a1","秠秢秥秪秫秭秱秸秼稂稃稇稉稊稌稑稕稛稞稡稧稫稭稯稰稴稵稸稹稺穄穅穇穈穌穕穖穙穜穝穟穠穥穧穪穭穵穸穾窀窂窅窆窊窋窐窑窔窞窠窣窬窳窵窹窻窼竆竉竌竎竑竛竨竩竫竬竱竴竻竽竾笇笔笟笣笧笩笪笫笭笮笯笰"],["8fd2a1","笱笴笽笿筀筁筇筎筕筠筤筦筩筪筭筯筲筳筷箄箉箎箐箑箖箛箞箠箥箬箯箰箲箵箶箺箻箼箽篂篅篈篊篔篖篗篙篚篛篨篪篲篴篵篸篹篺篼篾簁簂簃簄簆簉簋簌簎簏簙簛簠簥簦簨簬簱簳簴簶簹簺籆籊籕籑籒籓籙",5],["8fd3a1","籡籣籧籩籭籮籰籲籹籼籽粆粇粏粔粞粠粦粰粶粷粺粻粼粿糄糇糈糉糍糏糓糔糕糗糙糚糝糦糩糫糵紃紇紈紉紏紑紒紓紖紝紞紣紦紪紭紱紼紽紾絀絁絇絈絍絑絓絗絙絚絜絝絥絧絪絰絸絺絻絿綁綂綃綅綆綈綋綌綍綑綖綗綝"],["8fd4a1","綞綦綧綪綳綶綷綹緂",4,"緌緍緎緗緙縀緢緥緦緪緫緭緱緵緶緹緺縈縐縑縕縗縜縝縠縧縨縬縭縯縳縶縿繄繅繇繎繐繒繘繟繡繢繥繫繮繯繳繸繾纁纆纇纊纍纑纕纘纚纝纞缼缻缽缾缿罃罄罇罏罒罓罛罜罝罡罣罤罥罦罭"],["8fd5a1","罱罽罾罿羀羋羍羏羐羑羖羗羜羡羢羦羪羭羴羼羿翀翃翈翎翏翛翟翣翥翨翬翮翯翲翺翽翾翿耇耈耊耍耎耏耑耓耔耖耝耞耟耠耤耦耬耮耰耴耵耷耹耺耼耾聀聄聠聤聦聭聱聵肁肈肎肜肞肦肧肫肸肹胈胍胏胒胔胕胗胘胠胭胮"],["8fd6a1","胰胲胳胶胹胺胾脃脋脖脗脘脜脞脠脤脧脬脰脵脺脼腅腇腊腌腒腗腠腡腧腨腩腭腯腷膁膐膄膅膆膋膎膖膘膛膞膢膮膲膴膻臋臃臅臊臎臏臕臗臛臝臞臡臤臫臬臰臱臲臵臶臸臹臽臿舀舃舏舓舔舙舚舝舡舢舨舲舴舺艃艄艅艆"],["8fd7a1","艋艎艏艑艖艜艠艣艧艭艴艻艽艿芀芁芃芄芇芉芊芎芑芔芖芘芚芛芠芡芣芤芧芨芩芪芮芰芲芴芷芺芼芾芿苆苐苕苚苠苢苤苨苪苭苯苶苷苽苾茀茁茇茈茊茋荔茛茝茞茟茡茢茬茭茮茰茳茷茺茼茽荂荃荄荇荍荎荑荕荖荗荰荸"],["8fd8a1","荽荿莀莂莄莆莍莒莔莕莘莙莛莜莝莦莧莩莬莾莿菀菇菉菏菐菑菔菝荓菨菪菶菸菹菼萁萆萊萏萑萕萙莭萯萹葅葇葈葊葍葏葑葒葖葘葙葚葜葠葤葥葧葪葰葳葴葶葸葼葽蒁蒅蒒蒓蒕蒞蒦蒨蒩蒪蒯蒱蒴蒺蒽蒾蓀蓂蓇蓈蓌蓏蓓"],["8fd9a1","蓜蓧蓪蓯蓰蓱蓲蓷蔲蓺蓻蓽蔂蔃蔇蔌蔎蔐蔜蔞蔢蔣蔤蔥蔧蔪蔫蔯蔳蔴蔶蔿蕆蕏",4,"蕖蕙蕜",6,"蕤蕫蕯蕹蕺蕻蕽蕿薁薅薆薉薋薌薏薓薘薝薟薠薢薥薧薴薶薷薸薼薽薾薿藂藇藊藋藎薭藘藚藟藠藦藨藭藳藶藼"],["8fdaa1","藿蘀蘄蘅蘍蘎蘐蘑蘒蘘蘙蘛蘞蘡蘧蘩蘶蘸蘺蘼蘽虀虂虆虒虓虖虗虘虙虝虠",4,"虩虬虯虵虶虷虺蚍蚑蚖蚘蚚蚜蚡蚦蚧蚨蚭蚱蚳蚴蚵蚷蚸蚹蚿蛀蛁蛃蛅蛑蛒蛕蛗蛚蛜蛠蛣蛥蛧蚈蛺蛼蛽蜄蜅蜇蜋蜎蜏蜐蜓蜔蜙蜞蜟蜡蜣"],["8fdba1","蜨蜮蜯蜱蜲蜹蜺蜼蜽蜾蝀蝃蝅蝍蝘蝝蝡蝤蝥蝯蝱蝲蝻螃",6,"螋螌螐螓螕螗螘螙螞螠螣螧螬螭螮螱螵螾螿蟁蟈蟉蟊蟎蟕蟖蟙蟚蟜蟟蟢蟣蟤蟪蟫蟭蟱蟳蟸蟺蟿蠁蠃蠆蠉蠊蠋蠐蠙蠒蠓蠔蠘蠚蠛蠜蠞蠟蠨蠭蠮蠰蠲蠵"],["8fdca1","蠺蠼衁衃衅衈衉衊衋衎衑衕衖衘衚衜衟衠衤衩衱衹衻袀袘袚袛袜袟袠袨袪袺袽袾裀裊",4,"裑裒裓裛裞裧裯裰裱裵裷褁褆褍褎褏褕褖褘褙褚褜褠褦褧褨褰褱褲褵褹褺褾襀襂襅襆襉襏襒襗襚襛襜襡襢襣襫襮襰襳襵襺"],["8fdda1","襻襼襽覉覍覐覔覕覛覜覟覠覥覰覴覵覶覷覼觔",4,"觥觩觫觭觱觳觶觹觽觿訄訅訇訏訑訒訔訕訞訠訢訤訦訫訬訯訵訷訽訾詀詃詅詇詉詍詎詓詖詗詘詜詝詡詥詧詵詶詷詹詺詻詾詿誀誃誆誋誏誐誒誖誗誙誟誧誩誮誯誳"],["8fdea1","誶誷誻誾諃諆諈諉諊諑諓諔諕諗諝諟諬諰諴諵諶諼諿謅謆謋謑謜謞謟謊謭謰謷謼譂",4,"譈譒譓譔譙譍譞譣譭譶譸譹譼譾讁讄讅讋讍讏讔讕讜讞讟谸谹谽谾豅豇豉豋豏豑豓豔豗豘豛豝豙豣豤豦豨豩豭豳豵豶豻豾貆"],["8fdfa1","貇貋貐貒貓貙貛貜貤貹貺賅賆賉賋賏賖賕賙賝賡賨賬賯賰賲賵賷賸賾賿贁贃贉贒贗贛赥赩赬赮赿趂趄趈趍趐趑趕趞趟趠趦趫趬趯趲趵趷趹趻跀跅跆跇跈跊跎跑跔跕跗跙跤跥跧跬跰趼跱跲跴跽踁踄踅踆踋踑踔踖踠踡踢"],["8fe0a1","踣踦踧踱踳踶踷踸踹踽蹀蹁蹋蹍蹎蹏蹔蹛蹜蹝蹞蹡蹢蹩蹬蹭蹯蹰蹱蹹蹺蹻躂躃躉躐躒躕躚躛躝躞躢躧躩躭躮躳躵躺躻軀軁軃軄軇軏軑軔軜軨軮軰軱軷軹軺軭輀輂輇輈輏輐輖輗輘輞輠輡輣輥輧輨輬輭輮輴輵輶輷輺轀轁"],["8fe1a1","轃轇轏轑",4,"轘轝轞轥辝辠辡辤辥辦辵辶辸达迀迁迆迊迋迍运迒迓迕迠迣迤迨迮迱迵迶迻迾适逄逈逌逘逛逨逩逯逪逬逭逳逴逷逿遃遄遌遛遝遢遦遧遬遰遴遹邅邈邋邌邎邐邕邗邘邙邛邠邡邢邥邰邲邳邴邶邽郌邾郃"],["8fe2a1","郄郅郇郈郕郗郘郙郜郝郟郥郒郶郫郯郰郴郾郿鄀鄄鄅鄆鄈鄍鄐鄔鄖鄗鄘鄚鄜鄞鄠鄥鄢鄣鄧鄩鄮鄯鄱鄴鄶鄷鄹鄺鄼鄽酃酇酈酏酓酗酙酚酛酡酤酧酭酴酹酺酻醁醃醅醆醊醎醑醓醔醕醘醞醡醦醨醬醭醮醰醱醲醳醶醻醼醽醿"],["8fe3a1","釂釃釅釓釔釗釙釚釞釤釥釩釪釬",5,"釷釹釻釽鈀鈁鈄鈅鈆鈇鈉鈊鈌鈐鈒鈓鈖鈘鈜鈝鈣鈤鈥鈦鈨鈮鈯鈰鈳鈵鈶鈸鈹鈺鈼鈾鉀鉂鉃鉆鉇鉊鉍鉎鉏鉑鉘鉙鉜鉝鉠鉡鉥鉧鉨鉩鉮鉯鉰鉵",4,"鉻鉼鉽鉿銈銉銊銍銎銒銗"],["8fe4a1","銙銟銠銤銥銧銨銫銯銲銶銸銺銻銼銽銿",4,"鋅鋆鋇鋈鋋鋌鋍鋎鋐鋓鋕鋗鋘鋙鋜鋝鋟鋠鋡鋣鋥鋧鋨鋬鋮鋰鋹鋻鋿錀錂錈錍錑錔錕錜錝錞錟錡錤錥錧錩錪錳錴錶錷鍇鍈鍉鍐鍑鍒鍕鍗鍘鍚鍞鍤鍥鍧鍩鍪鍭鍯鍰鍱鍳鍴鍶"],["8fe5a1","鍺鍽鍿鎀鎁鎂鎈鎊鎋鎍鎏鎒鎕鎘鎛鎞鎡鎣鎤鎦鎨鎫鎴鎵鎶鎺鎩鏁鏄鏅鏆鏇鏉",4,"鏓鏙鏜鏞鏟鏢鏦鏧鏹鏷鏸鏺鏻鏽鐁鐂鐄鐈鐉鐍鐎鐏鐕鐖鐗鐟鐮鐯鐱鐲鐳鐴鐻鐿鐽鑃鑅鑈鑊鑌鑕鑙鑜鑟鑡鑣鑨鑫鑭鑮鑯鑱鑲钄钃镸镹"],["8fe6a1","镾閄閈閌閍閎閝閞閟閡閦閩閫閬閴閶閺閽閿闆闈闉闋闐闑闒闓闙闚闝闞闟闠闤闦阝阞阢阤阥阦阬阱阳阷阸阹阺阼阽陁陒陔陖陗陘陡陮陴陻陼陾陿隁隂隃隄隉隑隖隚隝隟隤隥隦隩隮隯隳隺雊雒嶲雘雚雝雞雟雩雯雱雺霂"],["8fe7a1","霃霅霉霚霛霝霡霢霣霨霱霳靁靃靊靎靏靕靗靘靚靛靣靧靪靮靳靶靷靸靻靽靿鞀鞉鞕鞖鞗鞙鞚鞞鞟鞢鞬鞮鞱鞲鞵鞶鞸鞹鞺鞼鞾鞿韁韄韅韇韉韊韌韍韎韐韑韔韗韘韙韝韞韠韛韡韤韯韱韴韷韸韺頇頊頙頍頎頔頖頜頞頠頣頦"],["8fe8a1","頫頮頯頰頲頳頵頥頾顄顇顊顑顒顓顖顗顙顚顢顣顥顦顪顬颫颭颮颰颴颷颸颺颻颿飂飅飈飌飡飣飥飦飧飪飳飶餂餇餈餑餕餖餗餚餛餜餟餢餦餧餫餱",4,"餹餺餻餼饀饁饆饇饈饍饎饔饘饙饛饜饞饟饠馛馝馟馦馰馱馲馵"],["8fe9a1","馹馺馽馿駃駉駓駔駙駚駜駞駧駪駫駬駰駴駵駹駽駾騂騃騄騋騌騐騑騖騞騠騢騣騤騧騭騮騳騵騶騸驇驁驄驊驋驌驎驑驔驖驝骪骬骮骯骲骴骵骶骹骻骾骿髁髃髆髈髎髐髒髕髖髗髛髜髠髤髥髧髩髬髲髳髵髹髺髽髿",4],["8feaa1","鬄鬅鬈鬉鬋鬌鬍鬎鬐鬒鬖鬙鬛鬜鬠鬦鬫鬭鬳鬴鬵鬷鬹鬺鬽魈魋魌魕魖魗魛魞魡魣魥魦魨魪",4,"魳魵魷魸魹魿鮀鮄鮅鮆鮇鮉鮊鮋鮍鮏鮐鮔鮚鮝鮞鮦鮧鮩鮬鮰鮱鮲鮷鮸鮻鮼鮾鮿鯁鯇鯈鯎鯐鯗鯘鯝鯟鯥鯧鯪鯫鯯鯳鯷鯸"],["8feba1","鯹鯺鯽鯿鰀鰂鰋鰏鰑鰖鰘鰙鰚鰜鰞鰢鰣鰦",4,"鰱鰵鰶鰷鰽鱁鱃鱄鱅鱉鱊鱎鱏鱐鱓鱔鱖鱘鱛鱝鱞鱟鱣鱩鱪鱜鱫鱨鱮鱰鱲鱵鱷鱻鳦鳲鳷鳹鴋鴂鴑鴗鴘鴜鴝鴞鴯鴰鴲鴳鴴鴺鴼鵅鴽鵂鵃鵇鵊鵓鵔鵟鵣鵢鵥鵩鵪鵫鵰鵶鵷鵻"],["8feca1","鵼鵾鶃鶄鶆鶊鶍鶎鶒鶓鶕鶖鶗鶘鶡鶪鶬鶮鶱鶵鶹鶼鶿鷃鷇鷉鷊鷔鷕鷖鷗鷚鷞鷟鷠鷥鷧鷩鷫鷮鷰鷳鷴鷾鸊鸂鸇鸎鸐鸑鸒鸕鸖鸙鸜鸝鹺鹻鹼麀麂麃麄麅麇麎麏麖麘麛麞麤麨麬麮麯麰麳麴麵黆黈黋黕黟黤黧黬黭黮黰黱黲黵"],["8feda1","黸黿鼂鼃鼉鼏鼐鼑鼒鼔鼖鼗鼙鼚鼛鼟鼢鼦鼪鼫鼯鼱鼲鼴鼷鼹鼺鼼鼽鼿齁齃",4,"齓齕齖齗齘齚齝齞齨齩齭",4,"齳齵齺齽龏龐龑龒龔龖龗龞龡龢龣龥"]]');

/***/ }),

/***/ 99129:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('{"uChars":[128,165,169,178,184,216,226,235,238,244,248,251,253,258,276,284,300,325,329,334,364,463,465,467,469,471,473,475,477,506,594,610,712,716,730,930,938,962,970,1026,1104,1106,8209,8215,8218,8222,8231,8241,8244,8246,8252,8365,8452,8454,8458,8471,8482,8556,8570,8596,8602,8713,8720,8722,8726,8731,8737,8740,8742,8748,8751,8760,8766,8777,8781,8787,8802,8808,8816,8854,8858,8870,8896,8979,9322,9372,9548,9588,9616,9622,9634,9652,9662,9672,9676,9680,9702,9735,9738,9793,9795,11906,11909,11913,11917,11928,11944,11947,11951,11956,11960,11964,11979,12284,12292,12312,12319,12330,12351,12436,12447,12535,12543,12586,12842,12850,12964,13200,13215,13218,13253,13263,13267,13270,13384,13428,13727,13839,13851,14617,14703,14801,14816,14964,15183,15471,15585,16471,16736,17208,17325,17330,17374,17623,17997,18018,18212,18218,18301,18318,18760,18811,18814,18820,18823,18844,18848,18872,19576,19620,19738,19887,40870,59244,59336,59367,59413,59417,59423,59431,59437,59443,59452,59460,59478,59493,63789,63866,63894,63976,63986,64016,64018,64021,64025,64034,64037,64042,65074,65093,65107,65112,65127,65132,65375,65510,65536],"gbChars":[0,36,38,45,50,81,89,95,96,100,103,104,105,109,126,133,148,172,175,179,208,306,307,308,309,310,311,312,313,341,428,443,544,545,558,741,742,749,750,805,819,820,7922,7924,7925,7927,7934,7943,7944,7945,7950,8062,8148,8149,8152,8164,8174,8236,8240,8262,8264,8374,8380,8381,8384,8388,8390,8392,8393,8394,8396,8401,8406,8416,8419,8424,8437,8439,8445,8482,8485,8496,8521,8603,8936,8946,9046,9050,9063,9066,9076,9092,9100,9108,9111,9113,9131,9162,9164,9218,9219,11329,11331,11334,11336,11346,11361,11363,11366,11370,11372,11375,11389,11682,11686,11687,11692,11694,11714,11716,11723,11725,11730,11736,11982,11989,12102,12336,12348,12350,12384,12393,12395,12397,12510,12553,12851,12962,12973,13738,13823,13919,13933,14080,14298,14585,14698,15583,15847,16318,16434,16438,16481,16729,17102,17122,17315,17320,17402,17418,17859,17909,17911,17915,17916,17936,17939,17961,18664,18703,18814,18962,19043,33469,33470,33471,33484,33485,33490,33497,33501,33505,33513,33520,33536,33550,37845,37921,37948,38029,38038,38064,38065,38066,38069,38075,38076,38078,39108,39109,39113,39114,39115,39116,39265,39394,189000]}');

/***/ }),

/***/ 55914:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["a140","",62],["a180","",32],["a240","",62],["a280","",32],["a2ab","",5],["a2e3","€"],["a2ef",""],["a2fd",""],["a340","",62],["a380","",31,"　"],["a440","",62],["a480","",32],["a4f4","",10],["a540","",62],["a580","",32],["a5f7","",7],["a640","",62],["a680","",32],["a6b9","",7],["a6d9","",6],["a6ec",""],["a6f3",""],["a6f6","",8],["a740","",62],["a780","",32],["a7c2","",14],["a7f2","",12],["a896","",10],["a8bc","ḿ"],["a8bf","ǹ"],["a8c1",""],["a8ea","",20],["a958",""],["a95b",""],["a95d",""],["a989","〾⿰",11],["a997","",12],["a9f0","",14],["aaa1","",93],["aba1","",93],["aca1","",93],["ada1","",93],["aea1","",93],["afa1","",93],["d7fa","",4],["f8a1","",93],["f9a1","",93],["faa1","",93],["fba1","",93],["fca1","",93],["fda1","",93],["fe50","⺁⺄㑳㑇⺈⺋㖞㘚㘎⺌⺗㥮㤘㧏㧟㩳㧐㭎㱮㳠⺧⺪䁖䅟⺮䌷⺳⺶⺷䎱䎬⺻䏝䓖䙡䙌"],["fe80","䜣䜩䝼䞍⻊䥇䥺䥽䦂䦃䦅䦆䦟䦛䦷䦶䲣䲟䲠䲡䱷䲢䴓",6,"䶮",93],["8135f437",""]]');

/***/ }),

/***/ 40679:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('[["0","\\u0000",128],["a1","｡",62],["8140","　、。，．・：；？！゛゜´｀¨＾￣＿ヽヾゝゞ〃仝々〆〇ー―‐／＼～∥｜…‥‘’“”（）〔〕［］｛｝〈",9,"＋－±×"],["8180","÷＝≠＜＞≦≧∞∴♂♀°′″℃￥＄￠￡％＃＆＊＠§☆★○●◎◇◆□■△▲▽▼※〒→←↑↓〓"],["81b8","∈∋⊆⊇⊂⊃∪∩"],["81c8","∧∨￢⇒⇔∀∃"],["81da","∠⊥⌒∂∇≡≒≪≫√∽∝∵∫∬"],["81f0","Å‰♯♭♪†‡¶"],["81fc","◯"],["824f","０",9],["8260","Ａ",25],["8281","ａ",25],["829f","ぁ",82],["8340","ァ",62],["8380","ム",22],["839f","Α",16,"Σ",6],["83bf","α",16,"σ",6],["8440","А",5,"ЁЖ",25],["8470","а",5,"ёж",7],["8480","о",17],["849f","─│┌┐┘└├┬┤┴┼━┃┏┓┛┗┣┳┫┻╋┠┯┨┷┿┝┰┥┸╂"],["8740","①",19,"Ⅰ",9],["875f","㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡"],["877e","㍻"],["8780","〝〟№㏍℡㊤",4,"㈱㈲㈹㍾㍽㍼≒≡∫∮∑√⊥∠∟⊿∵∩∪"],["889f","亜唖娃阿哀愛挨姶逢葵茜穐悪握渥旭葦芦鯵梓圧斡扱宛姐虻飴絢綾鮎或粟袷安庵按暗案闇鞍杏以伊位依偉囲夷委威尉惟意慰易椅為畏異移維緯胃萎衣謂違遺医井亥域育郁磯一壱溢逸稲茨芋鰯允印咽員因姻引飲淫胤蔭"],["8940","院陰隠韻吋右宇烏羽迂雨卯鵜窺丑碓臼渦嘘唄欝蔚鰻姥厩浦瓜閏噂云運雲荏餌叡営嬰影映曳栄永泳洩瑛盈穎頴英衛詠鋭液疫益駅悦謁越閲榎厭円"],["8980","園堰奄宴延怨掩援沿演炎焔煙燕猿縁艶苑薗遠鉛鴛塩於汚甥凹央奥往応押旺横欧殴王翁襖鴬鴎黄岡沖荻億屋憶臆桶牡乙俺卸恩温穏音下化仮何伽価佳加可嘉夏嫁家寡科暇果架歌河火珂禍禾稼箇花苛茄荷華菓蝦課嘩貨迦過霞蚊俄峨我牙画臥芽蛾賀雅餓駕介会解回塊壊廻快怪悔恢懐戒拐改"],["8a40","魁晦械海灰界皆絵芥蟹開階貝凱劾外咳害崖慨概涯碍蓋街該鎧骸浬馨蛙垣柿蛎鈎劃嚇各廓拡撹格核殻獲確穫覚角赫較郭閣隔革学岳楽額顎掛笠樫"],["8a80","橿梶鰍潟割喝恰括活渇滑葛褐轄且鰹叶椛樺鞄株兜竃蒲釜鎌噛鴨栢茅萱粥刈苅瓦乾侃冠寒刊勘勧巻喚堪姦完官寛干幹患感慣憾換敢柑桓棺款歓汗漢澗潅環甘監看竿管簡緩缶翰肝艦莞観諌貫還鑑間閑関陥韓館舘丸含岸巌玩癌眼岩翫贋雁頑顔願企伎危喜器基奇嬉寄岐希幾忌揮机旗既期棋棄"],["8b40","機帰毅気汽畿祈季稀紀徽規記貴起軌輝飢騎鬼亀偽儀妓宜戯技擬欺犠疑祇義蟻誼議掬菊鞠吉吃喫桔橘詰砧杵黍却客脚虐逆丘久仇休及吸宮弓急救"],["8b80","朽求汲泣灸球究窮笈級糾給旧牛去居巨拒拠挙渠虚許距鋸漁禦魚亨享京供侠僑兇競共凶協匡卿叫喬境峡強彊怯恐恭挟教橋況狂狭矯胸脅興蕎郷鏡響饗驚仰凝尭暁業局曲極玉桐粁僅勤均巾錦斤欣欽琴禁禽筋緊芹菌衿襟謹近金吟銀九倶句区狗玖矩苦躯駆駈駒具愚虞喰空偶寓遇隅串櫛釧屑屈"],["8c40","掘窟沓靴轡窪熊隈粂栗繰桑鍬勲君薫訓群軍郡卦袈祁係傾刑兄啓圭珪型契形径恵慶慧憩掲携敬景桂渓畦稽系経継繋罫茎荊蛍計詣警軽頚鶏芸迎鯨"],["8c80","劇戟撃激隙桁傑欠決潔穴結血訣月件倹倦健兼券剣喧圏堅嫌建憲懸拳捲検権牽犬献研硯絹県肩見謙賢軒遣鍵険顕験鹸元原厳幻弦減源玄現絃舷言諺限乎個古呼固姑孤己庫弧戸故枯湖狐糊袴股胡菰虎誇跨鈷雇顧鼓五互伍午呉吾娯後御悟梧檎瑚碁語誤護醐乞鯉交佼侯候倖光公功効勾厚口向"],["8d40","后喉坑垢好孔孝宏工巧巷幸広庚康弘恒慌抗拘控攻昂晃更杭校梗構江洪浩港溝甲皇硬稿糠紅紘絞綱耕考肯肱腔膏航荒行衡講貢購郊酵鉱砿鋼閤降"],["8d80","項香高鴻剛劫号合壕拷濠豪轟麹克刻告国穀酷鵠黒獄漉腰甑忽惚骨狛込此頃今困坤墾婚恨懇昏昆根梱混痕紺艮魂些佐叉唆嵯左差査沙瑳砂詐鎖裟坐座挫債催再最哉塞妻宰彩才採栽歳済災采犀砕砦祭斎細菜裁載際剤在材罪財冴坂阪堺榊肴咲崎埼碕鷺作削咋搾昨朔柵窄策索錯桜鮭笹匙冊刷"],["8e40","察拶撮擦札殺薩雑皐鯖捌錆鮫皿晒三傘参山惨撒散桟燦珊産算纂蚕讃賛酸餐斬暫残仕仔伺使刺司史嗣四士始姉姿子屍市師志思指支孜斯施旨枝止"],["8e80","死氏獅祉私糸紙紫肢脂至視詞詩試誌諮資賜雌飼歯事似侍児字寺慈持時次滋治爾璽痔磁示而耳自蒔辞汐鹿式識鴫竺軸宍雫七叱執失嫉室悉湿漆疾質実蔀篠偲柴芝屡蕊縞舎写射捨赦斜煮社紗者謝車遮蛇邪借勺尺杓灼爵酌釈錫若寂弱惹主取守手朱殊狩珠種腫趣酒首儒受呪寿授樹綬需囚収周"],["8f40","宗就州修愁拾洲秀秋終繍習臭舟蒐衆襲讐蹴輯週酋酬集醜什住充十従戎柔汁渋獣縦重銃叔夙宿淑祝縮粛塾熟出術述俊峻春瞬竣舜駿准循旬楯殉淳"],["8f80","準潤盾純巡遵醇順処初所暑曙渚庶緒署書薯藷諸助叙女序徐恕鋤除傷償勝匠升召哨商唱嘗奨妾娼宵将小少尚庄床廠彰承抄招掌捷昇昌昭晶松梢樟樵沼消渉湘焼焦照症省硝礁祥称章笑粧紹肖菖蒋蕉衝裳訟証詔詳象賞醤鉦鍾鐘障鞘上丈丞乗冗剰城場壌嬢常情擾条杖浄状畳穣蒸譲醸錠嘱埴飾"],["9040","拭植殖燭織職色触食蝕辱尻伸信侵唇娠寝審心慎振新晋森榛浸深申疹真神秦紳臣芯薪親診身辛進針震人仁刃塵壬尋甚尽腎訊迅陣靭笥諏須酢図厨"],["9080","逗吹垂帥推水炊睡粋翠衰遂酔錐錘随瑞髄崇嵩数枢趨雛据杉椙菅頗雀裾澄摺寸世瀬畝是凄制勢姓征性成政整星晴棲栖正清牲生盛精聖声製西誠誓請逝醒青静斉税脆隻席惜戚斥昔析石積籍績脊責赤跡蹟碩切拙接摂折設窃節説雪絶舌蝉仙先千占宣専尖川戦扇撰栓栴泉浅洗染潜煎煽旋穿箭線"],["9140","繊羨腺舛船薦詮賎践選遷銭銑閃鮮前善漸然全禅繕膳糎噌塑岨措曾曽楚狙疏疎礎祖租粗素組蘇訴阻遡鼠僧創双叢倉喪壮奏爽宋層匝惣想捜掃挿掻"],["9180","操早曹巣槍槽漕燥争痩相窓糟総綜聡草荘葬蒼藻装走送遭鎗霜騒像増憎臓蔵贈造促側則即息捉束測足速俗属賊族続卒袖其揃存孫尊損村遜他多太汰詑唾堕妥惰打柁舵楕陀駄騨体堆対耐岱帯待怠態戴替泰滞胎腿苔袋貸退逮隊黛鯛代台大第醍題鷹滝瀧卓啄宅托択拓沢濯琢託鐸濁諾茸凧蛸只"],["9240","叩但達辰奪脱巽竪辿棚谷狸鱈樽誰丹単嘆坦担探旦歎淡湛炭短端箪綻耽胆蛋誕鍛団壇弾断暖檀段男談値知地弛恥智池痴稚置致蜘遅馳築畜竹筑蓄"],["9280","逐秩窒茶嫡着中仲宙忠抽昼柱注虫衷註酎鋳駐樗瀦猪苧著貯丁兆凋喋寵帖帳庁弔張彫徴懲挑暢朝潮牒町眺聴脹腸蝶調諜超跳銚長頂鳥勅捗直朕沈珍賃鎮陳津墜椎槌追鎚痛通塚栂掴槻佃漬柘辻蔦綴鍔椿潰坪壷嬬紬爪吊釣鶴亭低停偵剃貞呈堤定帝底庭廷弟悌抵挺提梯汀碇禎程締艇訂諦蹄逓"],["9340","邸鄭釘鼎泥摘擢敵滴的笛適鏑溺哲徹撤轍迭鉄典填天展店添纏甜貼転顛点伝殿澱田電兎吐堵塗妬屠徒斗杜渡登菟賭途都鍍砥砺努度土奴怒倒党冬"],["9380","凍刀唐塔塘套宕島嶋悼投搭東桃梼棟盗淘湯涛灯燈当痘祷等答筒糖統到董蕩藤討謄豆踏逃透鐙陶頭騰闘働動同堂導憧撞洞瞳童胴萄道銅峠鴇匿得徳涜特督禿篤毒独読栃橡凸突椴届鳶苫寅酉瀞噸屯惇敦沌豚遁頓呑曇鈍奈那内乍凪薙謎灘捺鍋楢馴縄畷南楠軟難汝二尼弐迩匂賑肉虹廿日乳入"],["9440","如尿韮任妊忍認濡禰祢寧葱猫熱年念捻撚燃粘乃廼之埜嚢悩濃納能脳膿農覗蚤巴把播覇杷波派琶破婆罵芭馬俳廃拝排敗杯盃牌背肺輩配倍培媒梅"],["9480","楳煤狽買売賠陪這蝿秤矧萩伯剥博拍柏泊白箔粕舶薄迫曝漠爆縛莫駁麦函箱硲箸肇筈櫨幡肌畑畠八鉢溌発醗髪伐罰抜筏閥鳩噺塙蛤隼伴判半反叛帆搬斑板氾汎版犯班畔繁般藩販範釆煩頒飯挽晩番盤磐蕃蛮匪卑否妃庇彼悲扉批披斐比泌疲皮碑秘緋罷肥被誹費避非飛樋簸備尾微枇毘琵眉美"],["9540","鼻柊稗匹疋髭彦膝菱肘弼必畢筆逼桧姫媛紐百謬俵彪標氷漂瓢票表評豹廟描病秒苗錨鋲蒜蛭鰭品彬斌浜瀕貧賓頻敏瓶不付埠夫婦富冨布府怖扶敷"],["9580","斧普浮父符腐膚芙譜負賦赴阜附侮撫武舞葡蕪部封楓風葺蕗伏副復幅服福腹複覆淵弗払沸仏物鮒分吻噴墳憤扮焚奮粉糞紛雰文聞丙併兵塀幣平弊柄並蔽閉陛米頁僻壁癖碧別瞥蔑箆偏変片篇編辺返遍便勉娩弁鞭保舗鋪圃捕歩甫補輔穂募墓慕戊暮母簿菩倣俸包呆報奉宝峰峯崩庖抱捧放方朋"],["9640","法泡烹砲縫胞芳萌蓬蜂褒訪豊邦鋒飽鳳鵬乏亡傍剖坊妨帽忘忙房暴望某棒冒紡肪膨謀貌貿鉾防吠頬北僕卜墨撲朴牧睦穆釦勃没殆堀幌奔本翻凡盆"],["9680","摩磨魔麻埋妹昧枚毎哩槙幕膜枕鮪柾鱒桝亦俣又抹末沫迄侭繭麿万慢満漫蔓味未魅巳箕岬密蜜湊蓑稔脈妙粍民眠務夢無牟矛霧鵡椋婿娘冥名命明盟迷銘鳴姪牝滅免棉綿緬面麺摸模茂妄孟毛猛盲網耗蒙儲木黙目杢勿餅尤戻籾貰問悶紋門匁也冶夜爺耶野弥矢厄役約薬訳躍靖柳薮鑓愉愈油癒"],["9740","諭輸唯佑優勇友宥幽悠憂揖有柚湧涌猶猷由祐裕誘遊邑郵雄融夕予余与誉輿預傭幼妖容庸揚揺擁曜楊様洋溶熔用窯羊耀葉蓉要謡踊遥陽養慾抑欲"],["9780","沃浴翌翼淀羅螺裸来莱頼雷洛絡落酪乱卵嵐欄濫藍蘭覧利吏履李梨理璃痢裏裡里離陸律率立葎掠略劉流溜琉留硫粒隆竜龍侶慮旅虜了亮僚両凌寮料梁涼猟療瞭稜糧良諒遼量陵領力緑倫厘林淋燐琳臨輪隣鱗麟瑠塁涙累類令伶例冷励嶺怜玲礼苓鈴隷零霊麗齢暦歴列劣烈裂廉恋憐漣煉簾練聯"],["9840","蓮連錬呂魯櫓炉賂路露労婁廊弄朗楼榔浪漏牢狼篭老聾蝋郎六麓禄肋録論倭和話歪賄脇惑枠鷲亙亘鰐詫藁蕨椀湾碗腕"],["989f","弌丐丕个丱丶丼丿乂乖乘亂亅豫亊舒弍于亞亟亠亢亰亳亶从仍仄仆仂仗仞仭仟价伉佚估佛佝佗佇佶侈侏侘佻佩佰侑佯來侖儘俔俟俎俘俛俑俚俐俤俥倚倨倔倪倥倅伜俶倡倩倬俾俯們倆偃假會偕偐偈做偖偬偸傀傚傅傴傲"],["9940","僉僊傳僂僖僞僥僭僣僮價僵儉儁儂儖儕儔儚儡儺儷儼儻儿兀兒兌兔兢竸兩兪兮冀冂囘册冉冏冑冓冕冖冤冦冢冩冪冫决冱冲冰况冽凅凉凛几處凩凭"],["9980","凰凵凾刄刋刔刎刧刪刮刳刹剏剄剋剌剞剔剪剴剩剳剿剽劍劔劒剱劈劑辨辧劬劭劼劵勁勍勗勞勣勦飭勠勳勵勸勹匆匈甸匍匐匏匕匚匣匯匱匳匸區卆卅丗卉卍凖卞卩卮夘卻卷厂厖厠厦厥厮厰厶參簒雙叟曼燮叮叨叭叺吁吽呀听吭吼吮吶吩吝呎咏呵咎呟呱呷呰咒呻咀呶咄咐咆哇咢咸咥咬哄哈咨"],["9a40","咫哂咤咾咼哘哥哦唏唔哽哮哭哺哢唹啀啣啌售啜啅啖啗唸唳啝喙喀咯喊喟啻啾喘喞單啼喃喩喇喨嗚嗅嗟嗄嗜嗤嗔嘔嗷嘖嗾嗽嘛嗹噎噐營嘴嘶嘲嘸"],["9a80","噫噤嘯噬噪嚆嚀嚊嚠嚔嚏嚥嚮嚶嚴囂嚼囁囃囀囈囎囑囓囗囮囹圀囿圄圉圈國圍圓團圖嗇圜圦圷圸坎圻址坏坩埀垈坡坿垉垓垠垳垤垪垰埃埆埔埒埓堊埖埣堋堙堝塲堡塢塋塰毀塒堽塹墅墹墟墫墺壞墻墸墮壅壓壑壗壙壘壥壜壤壟壯壺壹壻壼壽夂夊夐夛梦夥夬夭夲夸夾竒奕奐奎奚奘奢奠奧奬奩"],["9b40","奸妁妝佞侫妣妲姆姨姜妍姙姚娥娟娑娜娉娚婀婬婉娵娶婢婪媚媼媾嫋嫂媽嫣嫗嫦嫩嫖嫺嫻嬌嬋嬖嬲嫐嬪嬶嬾孃孅孀孑孕孚孛孥孩孰孳孵學斈孺宀"],["9b80","它宦宸寃寇寉寔寐寤實寢寞寥寫寰寶寳尅將專對尓尠尢尨尸尹屁屆屎屓屐屏孱屬屮乢屶屹岌岑岔妛岫岻岶岼岷峅岾峇峙峩峽峺峭嶌峪崋崕崗嵜崟崛崑崔崢崚崙崘嵌嵒嵎嵋嵬嵳嵶嶇嶄嶂嶢嶝嶬嶮嶽嶐嶷嶼巉巍巓巒巖巛巫已巵帋帚帙帑帛帶帷幄幃幀幎幗幔幟幢幤幇幵并幺麼广庠廁廂廈廐廏"],["9c40","廖廣廝廚廛廢廡廨廩廬廱廳廰廴廸廾弃弉彝彜弋弑弖弩弭弸彁彈彌彎弯彑彖彗彙彡彭彳彷徃徂彿徊很徑徇從徙徘徠徨徭徼忖忻忤忸忱忝悳忿怡恠"],["9c80","怙怐怩怎怱怛怕怫怦怏怺恚恁恪恷恟恊恆恍恣恃恤恂恬恫恙悁悍惧悃悚悄悛悖悗悒悧悋惡悸惠惓悴忰悽惆悵惘慍愕愆惶惷愀惴惺愃愡惻惱愍愎慇愾愨愧慊愿愼愬愴愽慂慄慳慷慘慙慚慫慴慯慥慱慟慝慓慵憙憖憇憬憔憚憊憑憫憮懌懊應懷懈懃懆憺懋罹懍懦懣懶懺懴懿懽懼懾戀戈戉戍戌戔戛"],["9d40","戞戡截戮戰戲戳扁扎扞扣扛扠扨扼抂抉找抒抓抖拔抃抔拗拑抻拏拿拆擔拈拜拌拊拂拇抛拉挌拮拱挧挂挈拯拵捐挾捍搜捏掖掎掀掫捶掣掏掉掟掵捫"],["9d80","捩掾揩揀揆揣揉插揶揄搖搴搆搓搦搶攝搗搨搏摧摯摶摎攪撕撓撥撩撈撼據擒擅擇撻擘擂擱擧舉擠擡抬擣擯攬擶擴擲擺攀擽攘攜攅攤攣攫攴攵攷收攸畋效敖敕敍敘敞敝敲數斂斃變斛斟斫斷旃旆旁旄旌旒旛旙无旡旱杲昊昃旻杳昵昶昴昜晏晄晉晁晞晝晤晧晨晟晢晰暃暈暎暉暄暘暝曁暹曉暾暼"],["9e40","曄暸曖曚曠昿曦曩曰曵曷朏朖朞朦朧霸朮朿朶杁朸朷杆杞杠杙杣杤枉杰枩杼杪枌枋枦枡枅枷柯枴柬枳柩枸柤柞柝柢柮枹柎柆柧檜栞框栩桀桍栲桎"],["9e80","梳栫桙档桷桿梟梏梭梔條梛梃檮梹桴梵梠梺椏梍桾椁棊椈棘椢椦棡椌棍棔棧棕椶椒椄棗棣椥棹棠棯椨椪椚椣椡棆楹楷楜楸楫楔楾楮椹楴椽楙椰楡楞楝榁楪榲榮槐榿槁槓榾槎寨槊槝榻槃榧樮榑榠榜榕榴槞槨樂樛槿權槹槲槧樅榱樞槭樔槫樊樒櫁樣樓橄樌橲樶橸橇橢橙橦橈樸樢檐檍檠檄檢檣"],["9f40","檗蘗檻櫃櫂檸檳檬櫞櫑櫟檪櫚櫪櫻欅蘖櫺欒欖鬱欟欸欷盜欹飮歇歃歉歐歙歔歛歟歡歸歹歿殀殄殃殍殘殕殞殤殪殫殯殲殱殳殷殼毆毋毓毟毬毫毳毯"],["9f80","麾氈氓气氛氤氣汞汕汢汪沂沍沚沁沛汾汨汳沒沐泄泱泓沽泗泅泝沮沱沾沺泛泯泙泪洟衍洶洫洽洸洙洵洳洒洌浣涓浤浚浹浙涎涕濤涅淹渕渊涵淇淦涸淆淬淞淌淨淒淅淺淙淤淕淪淮渭湮渮渙湲湟渾渣湫渫湶湍渟湃渺湎渤滿渝游溂溪溘滉溷滓溽溯滄溲滔滕溏溥滂溟潁漑灌滬滸滾漿滲漱滯漲滌"],["e040","漾漓滷澆潺潸澁澀潯潛濳潭澂潼潘澎澑濂潦澳澣澡澤澹濆澪濟濕濬濔濘濱濮濛瀉瀋濺瀑瀁瀏濾瀛瀚潴瀝瀘瀟瀰瀾瀲灑灣炙炒炯烱炬炸炳炮烟烋烝"],["e080","烙焉烽焜焙煥煕熈煦煢煌煖煬熏燻熄熕熨熬燗熹熾燒燉燔燎燠燬燧燵燼燹燿爍爐爛爨爭爬爰爲爻爼爿牀牆牋牘牴牾犂犁犇犒犖犢犧犹犲狃狆狄狎狒狢狠狡狹狷倏猗猊猜猖猝猴猯猩猥猾獎獏默獗獪獨獰獸獵獻獺珈玳珎玻珀珥珮珞璢琅瑯琥珸琲琺瑕琿瑟瑙瑁瑜瑩瑰瑣瑪瑶瑾璋璞璧瓊瓏瓔珱"],["e140","瓠瓣瓧瓩瓮瓲瓰瓱瓸瓷甄甃甅甌甎甍甕甓甞甦甬甼畄畍畊畉畛畆畚畩畤畧畫畭畸當疆疇畴疊疉疂疔疚疝疥疣痂疳痃疵疽疸疼疱痍痊痒痙痣痞痾痿"],["e180","痼瘁痰痺痲痳瘋瘍瘉瘟瘧瘠瘡瘢瘤瘴瘰瘻癇癈癆癜癘癡癢癨癩癪癧癬癰癲癶癸發皀皃皈皋皎皖皓皙皚皰皴皸皹皺盂盍盖盒盞盡盥盧盪蘯盻眈眇眄眩眤眞眥眦眛眷眸睇睚睨睫睛睥睿睾睹瞎瞋瞑瞠瞞瞰瞶瞹瞿瞼瞽瞻矇矍矗矚矜矣矮矼砌砒礦砠礪硅碎硴碆硼碚碌碣碵碪碯磑磆磋磔碾碼磅磊磬"],["e240","磧磚磽磴礇礒礑礙礬礫祀祠祗祟祚祕祓祺祿禊禝禧齋禪禮禳禹禺秉秕秧秬秡秣稈稍稘稙稠稟禀稱稻稾稷穃穗穉穡穢穩龝穰穹穽窈窗窕窘窖窩竈窰"],["e280","窶竅竄窿邃竇竊竍竏竕竓站竚竝竡竢竦竭竰笂笏笊笆笳笘笙笞笵笨笶筐筺笄筍笋筌筅筵筥筴筧筰筱筬筮箝箘箟箍箜箚箋箒箏筝箙篋篁篌篏箴篆篝篩簑簔篦篥籠簀簇簓篳篷簗簍篶簣簧簪簟簷簫簽籌籃籔籏籀籐籘籟籤籖籥籬籵粃粐粤粭粢粫粡粨粳粲粱粮粹粽糀糅糂糘糒糜糢鬻糯糲糴糶糺紆"],["e340","紂紜紕紊絅絋紮紲紿紵絆絳絖絎絲絨絮絏絣經綉絛綏絽綛綺綮綣綵緇綽綫總綢綯緜綸綟綰緘緝緤緞緻緲緡縅縊縣縡縒縱縟縉縋縢繆繦縻縵縹繃縷"],["e380","縲縺繧繝繖繞繙繚繹繪繩繼繻纃緕繽辮繿纈纉續纒纐纓纔纖纎纛纜缸缺罅罌罍罎罐网罕罔罘罟罠罨罩罧罸羂羆羃羈羇羌羔羞羝羚羣羯羲羹羮羶羸譱翅翆翊翕翔翡翦翩翳翹飜耆耄耋耒耘耙耜耡耨耿耻聊聆聒聘聚聟聢聨聳聲聰聶聹聽聿肄肆肅肛肓肚肭冐肬胛胥胙胝胄胚胖脉胯胱脛脩脣脯腋"],["e440","隋腆脾腓腑胼腱腮腥腦腴膃膈膊膀膂膠膕膤膣腟膓膩膰膵膾膸膽臀臂膺臉臍臑臙臘臈臚臟臠臧臺臻臾舁舂舅與舊舍舐舖舩舫舸舳艀艙艘艝艚艟艤"],["e480","艢艨艪艫舮艱艷艸艾芍芒芫芟芻芬苡苣苟苒苴苳苺莓范苻苹苞茆苜茉苙茵茴茖茲茱荀茹荐荅茯茫茗茘莅莚莪莟莢莖茣莎莇莊荼莵荳荵莠莉莨菴萓菫菎菽萃菘萋菁菷萇菠菲萍萢萠莽萸蔆菻葭萪萼蕚蒄葷葫蒭葮蒂葩葆萬葯葹萵蓊葢蒹蒿蒟蓙蓍蒻蓚蓐蓁蓆蓖蒡蔡蓿蓴蔗蔘蔬蔟蔕蔔蓼蕀蕣蕘蕈"],["e540","蕁蘂蕋蕕薀薤薈薑薊薨蕭薔薛藪薇薜蕷蕾薐藉薺藏薹藐藕藝藥藜藹蘊蘓蘋藾藺蘆蘢蘚蘰蘿虍乕虔號虧虱蚓蚣蚩蚪蚋蚌蚶蚯蛄蛆蚰蛉蠣蚫蛔蛞蛩蛬"],["e580","蛟蛛蛯蜒蜆蜈蜀蜃蛻蜑蜉蜍蛹蜊蜴蜿蜷蜻蜥蜩蜚蝠蝟蝸蝌蝎蝴蝗蝨蝮蝙蝓蝣蝪蠅螢螟螂螯蟋螽蟀蟐雖螫蟄螳蟇蟆螻蟯蟲蟠蠏蠍蟾蟶蟷蠎蟒蠑蠖蠕蠢蠡蠱蠶蠹蠧蠻衄衂衒衙衞衢衫袁衾袞衵衽袵衲袂袗袒袮袙袢袍袤袰袿袱裃裄裔裘裙裝裹褂裼裴裨裲褄褌褊褓襃褞褥褪褫襁襄褻褶褸襌褝襠襞"],["e640","襦襤襭襪襯襴襷襾覃覈覊覓覘覡覩覦覬覯覲覺覽覿觀觚觜觝觧觴觸訃訖訐訌訛訝訥訶詁詛詒詆詈詼詭詬詢誅誂誄誨誡誑誥誦誚誣諄諍諂諚諫諳諧"],["e680","諤諱謔諠諢諷諞諛謌謇謚諡謖謐謗謠謳鞫謦謫謾謨譁譌譏譎證譖譛譚譫譟譬譯譴譽讀讌讎讒讓讖讙讚谺豁谿豈豌豎豐豕豢豬豸豺貂貉貅貊貍貎貔豼貘戝貭貪貽貲貳貮貶賈賁賤賣賚賽賺賻贄贅贊贇贏贍贐齎贓賍贔贖赧赭赱赳趁趙跂趾趺跏跚跖跌跛跋跪跫跟跣跼踈踉跿踝踞踐踟蹂踵踰踴蹊"],["e740","蹇蹉蹌蹐蹈蹙蹤蹠踪蹣蹕蹶蹲蹼躁躇躅躄躋躊躓躑躔躙躪躡躬躰軆躱躾軅軈軋軛軣軼軻軫軾輊輅輕輒輙輓輜輟輛輌輦輳輻輹轅轂輾轌轉轆轎轗轜"],["e780","轢轣轤辜辟辣辭辯辷迚迥迢迪迯邇迴逅迹迺逑逕逡逍逞逖逋逧逶逵逹迸遏遐遑遒逎遉逾遖遘遞遨遯遶隨遲邂遽邁邀邊邉邏邨邯邱邵郢郤扈郛鄂鄒鄙鄲鄰酊酖酘酣酥酩酳酲醋醉醂醢醫醯醪醵醴醺釀釁釉釋釐釖釟釡釛釼釵釶鈞釿鈔鈬鈕鈑鉞鉗鉅鉉鉤鉈銕鈿鉋鉐銜銖銓銛鉚鋏銹銷鋩錏鋺鍄錮"],["e840","錙錢錚錣錺錵錻鍜鍠鍼鍮鍖鎰鎬鎭鎔鎹鏖鏗鏨鏥鏘鏃鏝鏐鏈鏤鐚鐔鐓鐃鐇鐐鐶鐫鐵鐡鐺鑁鑒鑄鑛鑠鑢鑞鑪鈩鑰鑵鑷鑽鑚鑼鑾钁鑿閂閇閊閔閖閘閙"],["e880","閠閨閧閭閼閻閹閾闊濶闃闍闌闕闔闖關闡闥闢阡阨阮阯陂陌陏陋陷陜陞陝陟陦陲陬隍隘隕隗險隧隱隲隰隴隶隸隹雎雋雉雍襍雜霍雕雹霄霆霈霓霎霑霏霖霙霤霪霰霹霽霾靄靆靈靂靉靜靠靤靦靨勒靫靱靹鞅靼鞁靺鞆鞋鞏鞐鞜鞨鞦鞣鞳鞴韃韆韈韋韜韭齏韲竟韶韵頏頌頸頤頡頷頽顆顏顋顫顯顰"],["e940","顱顴顳颪颯颱颶飄飃飆飩飫餃餉餒餔餘餡餝餞餤餠餬餮餽餾饂饉饅饐饋饑饒饌饕馗馘馥馭馮馼駟駛駝駘駑駭駮駱駲駻駸騁騏騅駢騙騫騷驅驂驀驃"],["e980","騾驕驍驛驗驟驢驥驤驩驫驪骭骰骼髀髏髑髓體髞髟髢髣髦髯髫髮髴髱髷髻鬆鬘鬚鬟鬢鬣鬥鬧鬨鬩鬪鬮鬯鬲魄魃魏魍魎魑魘魴鮓鮃鮑鮖鮗鮟鮠鮨鮴鯀鯊鮹鯆鯏鯑鯒鯣鯢鯤鯔鯡鰺鯲鯱鯰鰕鰔鰉鰓鰌鰆鰈鰒鰊鰄鰮鰛鰥鰤鰡鰰鱇鰲鱆鰾鱚鱠鱧鱶鱸鳧鳬鳰鴉鴈鳫鴃鴆鴪鴦鶯鴣鴟鵄鴕鴒鵁鴿鴾鵆鵈"],["ea40","鵝鵞鵤鵑鵐鵙鵲鶉鶇鶫鵯鵺鶚鶤鶩鶲鷄鷁鶻鶸鶺鷆鷏鷂鷙鷓鷸鷦鷭鷯鷽鸚鸛鸞鹵鹹鹽麁麈麋麌麒麕麑麝麥麩麸麪麭靡黌黎黏黐黔黜點黝黠黥黨黯"],["ea80","黴黶黷黹黻黼黽鼇鼈皷鼕鼡鼬鼾齊齒齔齣齟齠齡齦齧齬齪齷齲齶龕龜龠堯槇遙瑤凜熙"],["ed40","纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏"],["ed80","塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱"],["ee40","犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙"],["ee80","蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑"],["eeef","ⅰ",9,"￢￤＇＂"],["f040","",62],["f080","",124],["f140","",62],["f180","",124],["f240","",62],["f280","",124],["f340","",62],["f380","",124],["f440","",62],["f480","",124],["f540","",62],["f580","",124],["f640","",62],["f680","",124],["f740","",62],["f780","",124],["f840","",62],["f880","",124],["f940",""],["fa40","ⅰ",9,"Ⅰ",9,"￢￤＇＂㈱№℡∵纊褜鍈銈蓜俉炻昱棈鋹曻彅丨仡仼伀伃伹佖侒侊侚侔俍偀倢俿倞偆偰偂傔僴僘兊"],["fa80","兤冝冾凬刕劜劦勀勛匀匇匤卲厓厲叝﨎咜咊咩哿喆坙坥垬埈埇﨏塚增墲夋奓奛奝奣妤妺孖寀甯寘寬尞岦岺峵崧嵓﨑嵂嵭嶸嶹巐弡弴彧德忞恝悅悊惞惕愠惲愑愷愰憘戓抦揵摠撝擎敎昀昕昻昉昮昞昤晥晗晙晴晳暙暠暲暿曺朎朗杦枻桒柀栁桄棏﨓楨﨔榘槢樰橫橆橳橾櫢櫤毖氿汜沆汯泚洄涇浯"],["fb40","涖涬淏淸淲淼渹湜渧渼溿澈澵濵瀅瀇瀨炅炫焏焄煜煆煇凞燁燾犱犾猤猪獷玽珉珖珣珒琇珵琦琪琩琮瑢璉璟甁畯皂皜皞皛皦益睆劯砡硎硤硺礰礼神"],["fb80","祥禔福禛竑竧靖竫箞精絈絜綷綠緖繒罇羡羽茁荢荿菇菶葈蒴蕓蕙蕫﨟薰蘒﨡蠇裵訒訷詹誧誾諟諸諶譓譿賰賴贒赶﨣軏﨤逸遧郞都鄕鄧釚釗釞釭釮釤釥鈆鈐鈊鈺鉀鈼鉎鉙鉑鈹鉧銧鉷鉸鋧鋗鋙鋐﨧鋕鋠鋓錥錡鋻﨨錞鋿錝錂鍰鍗鎤鏆鏞鏸鐱鑅鑈閒隆﨩隝隯霳霻靃靍靏靑靕顗顥飯飼餧館馞驎髙"],["fc40","髜魵魲鮏鮱鮻鰀鵰鵫鶴鸙黑"]]');

/***/ }),

/***/ 9240:
/***/ (function(module) {

"use strict";
module.exports = /*#__PURE__*/JSON.parse('{"ifd":{"8298":"Copyright","8769":"ExifIFDPointer","8822":"ExposureProgram","8824":"SpectralSensitivity","8825":"GPSInfoIFDPointer","8827":"PhotographicSensitivity","8828":"OECF","8830":"SensitivityType","8831":"StandardOutputSensitivity","8832":"RecommendedExposureIndex","8833":"ISOSpeed","8834":"ISOSpeedLatitudeyyy","8835":"ISOSpeedLatitudezzz","9000":"ExifVersion","9003":"DateTimeOriginal","9004":"DateTimeDigitized","9101":"ComponentsConfiguration","9102":"CompressedBitsPerPixel","9201":"ShutterSpeedValue","9202":"ApertureValue","9203":"BrightnessValue","9204":"ExposureBiasValue","9205":"MaxApertureValue","9206":"SubjectDistance","9207":"MeteringMode","9208":"LightSource","9209":"Flash","9214":"SubjectArea","9286":"UserComment","9290":"SubSecTime","9291":"SubSecTimeOriginal","9292":"SubSecTimeDigitized","010e":"ImageDescription","010f":"Make","011a":"XResolution","011b":"YResolution","011c":"PlanarConfiguration","012d":"TransferFunction","013b":"Artist","013e":"WhitePoint","013f":"PrimaryChromaticities","0100":"ImageWidth","0101":"ImageHeight","0102":"BitsPerSample","0103":"Compression","0106":"PhotometricInterpretation","0110":"Model","0111":"StripOffsets","0112":"Orientation","0115":"SamplesPerPixel","0116":"RowsPerStrip","0117":"StripByteCounts","0128":"ResolutionUnit","0131":"Software","0132":"DateTime","0201":"JPEGInterchangeFormat","0202":"JPEGInterchangeFormatLength","0211":"YCbCrCoefficients","0212":"YCbCrSubSampling","0213":"YCbCrPositioning","0214":"ReferenceBlackWhite","829a":"ExposureTime","829d":"FNumber","920a":"FocalLength","927c":"MakerNote","a000":"FlashpixVersion","a001":"ColorSpace","a002":"PixelXDimension","a003":"PixelYDimension","a004":"RelatedSoundFile","a005":"InteroperabilityIFDPointer","a20b":"FlashEnergy","a20c":"SpatialFrequencyResponse","a20e":"FocalPlaneXResolution","a20f":"FocalPlaneYResolution","a40a":"Sharpness","a40b":"DeviceSettingDescription","a40c":"SubjectDistanceRange","a210":"FocalPlaneResolutionUnit","a214":"SubjectLocation","a215":"ExposureIndex","a217":"SensingMethod","a300":"FileSource","a301":"SceneType","a302":"CFAPattern","a401":"CustomRendered","a402":"ExposureMode","a403":"WhiteBalance","a404":"DigitalZoomRatio","a405":"FocalLengthIn35mmFilm","a406":"SceneCaptureType","a407":"GainControl","a408":"Contrast","a409":"Saturation","a420":"ImageUniqueID","a430":"CameraOwnerName","a431":"BodySerialNumber","a432":"LensSpecification","a433":"LensMake","a434":"LensModel","a435":"LensSerialNumber","a500":"Gamma"},"gps":{"0000":"GPSVersionID","0001":"GPSLatitudeRef","0002":"GPSLatitude","0003":"GPSLongitudeRef","0004":"GPSLongitude","0005":"GPSAltitudeRef","0006":"GPSAltitude","0007":"GPSTimeStamp","0008":"GPSSatellites","0009":"GPSStatus","000a":"GPSMeasureMode","000b":"GPSDOP","000c":"GPSSpeedRef","000d":"GPSSpeed","000e":"GPSTrackRef","000f":"GPSTrack","0010":"GPSImgDirectionRef","0011":"GPSImgDirection","0012":"GPSMapDatum","0013":"GPSDestLatitudeRef","0014":"GPSDestLatitude","0015":"GPSDestLongitudeRef","0016":"GPSDestLongitude","0017":"GPSDestBearingRef","0018":"GPSDestBearing","0019":"GPSDestDistanceRef","001a":"GPSDestDistance","001b":"GPSProcessingMethod","001c":"GPSAreaInformation","001d":"GPSDateStamp","001e":"GPSDifferential","001f":"GPSHPositioningError"}}');

/***/ })

/******/ 	});
/************************************************************************/
/******/ 	// The module cache
/******/ 	var __webpack_module_cache__ = {};
/******/ 	
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/ 		// Check if module is in cache
/******/ 		var cachedModule = __webpack_module_cache__[moduleId];
/******/ 		if (cachedModule !== undefined) {
/******/ 			return cachedModule.exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = __webpack_module_cache__[moduleId] = {
/******/ 			id: moduleId,
/******/ 			loaded: false,
/******/ 			exports: {}
/******/ 		};
/******/ 	
/******/ 		// Execute the module function
/******/ 		__webpack_modules__[moduleId].call(module.exports, module, module.exports, __webpack_require__);
/******/ 	
/******/ 		// Flag the module as loaded
/******/ 		module.loaded = true;
/******/ 	
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/ 	
/************************************************************************/
/******/ 	/* webpack/runtime/global */
/******/ 	!function() {
/******/ 		__webpack_require__.g = (function() {
/******/ 			if (typeof globalThis === 'object') return globalThis;
/******/ 			try {
/******/ 				return this || new Function('return this')();
/******/ 			} catch (e) {
/******/ 				if (typeof window === 'object') return window;
/******/ 			}
/******/ 		})();
/******/ 	}();
/******/ 	
/******/ 	/* webpack/runtime/node module decorator */
/******/ 	!function() {
/******/ 		__webpack_require__.nmd = function(module) {
/******/ 			module.paths = [];
/******/ 			if (!module.children) module.children = [];
/******/ 			return module;
/******/ 		};
/******/ 	}();
/******/ 	
/************************************************************************/
/******/ 	
/******/ 	// startup
/******/ 	// Load entry module and return exports
/******/ 	// This entry module is referenced by other modules so it can't be inlined
/******/ 	var __webpack_exports__ = __webpack_require__(73751);
/******/ 	
/******/ 	return __webpack_exports__;
/******/ })()
;
});
//# sourceMappingURL=pdfmake.js.map
this.pdfMake = this.pdfMake || {}; this.pdfMake.vfs = {"NotoSansKR-Bold.otf":"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","NotoSansKR-Regular.otf":"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","Roboto-Regular.ttf":"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","Roboto-Italic.ttf":"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","Roboto-Medium.ttf":"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","Roboto-MediumItalic.ttf":"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"};
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global.jspdf = factory());
}(this, function () { 'use strict';

  var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && typeof Symbol === "function" && obj.constructor === Symbol ? "symbol" : typeof obj;
  };

  /** @preserve
   * jsPDF - PDF Document creation from JavaScript
   * Version 1.3.0 Built on 2016-09-28T10:52:40.410Z
   *                           CommitID a434267220
   *
   * Copyright (c) 2010-2014 James Hall <james@parall.ax>, https://github.com/MrRio/jsPDF
   *               2010 Aaron Spike, https://github.com/acspike
   *               2012 Willow Systems Corporation, willow-systems.com
   *               2012 Pablo Hess, https://github.com/pablohess
   *               2012 Florian Jenett, https://github.com/fjenett
   *               2013 Warren Weckesser, https://github.com/warrenweckesser
   *               2013 Youssef Beddad, https://github.com/lifof
   *               2013 Lee Driscoll, https://github.com/lsdriscoll
   *               2013 Stefan Slonevskiy, https://github.com/stefslon
   *               2013 Jeremy Morel, https://github.com/jmorel
   *               2013 Christoph Hartmann, https://github.com/chris-rock
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 James Makes, https://github.com/dollaruw
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 Steven Spungin, https://github.com/Flamenco
   *               2014 Kenneth Glassey, https://github.com/Gavvers
   *
   * Licensed under the MIT License
   *
   * Contributor(s):
   *    siefkenj, ahwolf, rickygu, Midnith, saintclair, eaparango,
   *    kim3er, mfo, alnorth, Flamenco
   */

  /**
   * Creates new jsPDF document object instance.
   *
   * @class
   * @param orientation One of "portrait" or "landscape" (or shortcuts "p" (Default), "l")
   * @param unit        Measurement unit to be used when coordinates are specified.
   *                    One of "pt" (points), "mm" (Default), "cm", "in"
   * @param format      One of 'pageFormats' as shown below, default: a4
   * @returns {jsPDF}
   * @name jsPDF
   */
  var jsPDF = function (global) {
      'use strict';

      var pdfVersion = '1.3',
          pageFormats = { // Size in pt of various paper formats
          'a0': [2383.94, 3370.39], 'a1': [1683.78, 2383.94],
          'a2': [1190.55, 1683.78], 'a3': [841.89, 1190.55],
          'a4': [595.28, 841.89], 'a5': [419.53, 595.28],
          'a6': [297.64, 419.53], 'a7': [209.76, 297.64],
          'a8': [147.40, 209.76], 'a9': [104.88, 147.40],
          'a10': [73.70, 104.88], 'b0': [2834.65, 4008.19],
          'b1': [2004.09, 2834.65], 'b2': [1417.32, 2004.09],
          'b3': [1000.63, 1417.32], 'b4': [708.66, 1000.63],
          'b5': [498.90, 708.66], 'b6': [354.33, 498.90],
          'b7': [249.45, 354.33], 'b8': [175.75, 249.45],
          'b9': [124.72, 175.75], 'b10': [87.87, 124.72],
          'c0': [2599.37, 3676.54], 'c1': [1836.85, 2599.37],
          'c2': [1298.27, 1836.85], 'c3': [918.43, 1298.27],
          'c4': [649.13, 918.43], 'c5': [459.21, 649.13],
          'c6': [323.15, 459.21], 'c7': [229.61, 323.15],
          'c8': [161.57, 229.61], 'c9': [113.39, 161.57],
          'c10': [79.37, 113.39], 'dl': [311.81, 623.62],
          'letter': [612, 792],
          'government-letter': [576, 756],
          'legal': [612, 1008],
          'junior-legal': [576, 360],
          'ledger': [1224, 792],
          'tabloid': [792, 1224],
          'credit-card': [153, 243]
      };

      /**
       * jsPDF's Internal PubSub Implementation.
       * See mrrio.github.io/jsPDF/doc/symbols/PubSub.html
       * Backward compatible rewritten on 2014 by
       * Diego Casorran, https://github.com/diegocr
       *
       * @class
       * @name PubSub
       */
      function PubSub(context) {
          var topics = {};

          this.subscribe = function (topic, callback, once) {
              if (typeof callback !== 'function') {
                  return false;
              }

              if (!topics.hasOwnProperty(topic)) {
                  topics[topic] = {};
              }

              var id = Math.random().toString(35);
              topics[topic][id] = [callback, !!once];

              return id;
          };

          this.unsubscribe = function (token) {
              for (var topic in topics) {
                  if (topics[topic][token]) {
                      delete topics[topic][token];
                      return true;
                  }
              }
              return false;
          };

          this.publish = function (topic) {
              if (topics.hasOwnProperty(topic)) {
                  var args = Array.prototype.slice.call(arguments, 1),
                      idr = [];

                  for (var id in topics[topic]) {
                      var sub = topics[topic][id];
                      try {
                          sub[0].apply(context, args);
                      } catch (ex) {
                          if (global.console) {
                              console.error('jsPDF PubSub Error', ex.message, ex);
                          }
                      }
                      if (sub[1]) idr.push(id);
                  }
                  if (idr.length) idr.forEach(this.unsubscribe);
              }
          };
      }

      /**
       * @constructor
       * @private
       */
      function jsPDF(orientation, unit, format, compressPdf) {
          var options = {};

          if ((typeof orientation === 'undefined' ? 'undefined' : _typeof(orientation)) === 'object') {
              options = orientation;

              orientation = options.orientation;
              unit = options.unit || unit;
              format = options.format || format;
              compressPdf = options.compress || options.compressPdf || compressPdf;
          }

          // Default options
          unit = unit || 'mm';
          format = format || 'a4';
          orientation = ('' + (orientation || 'P')).toLowerCase();

          var format_as_string = ('' + format).toLowerCase(),
              compress = !!compressPdf && typeof Uint8Array === 'function',
              textColor = options.textColor || '0 g',
              drawColor = options.drawColor || '0 G',
              activeFontSize = options.fontSize || 16,
              lineHeightProportion = options.lineHeight || 1.15,
              lineWidth = options.lineWidth || 0.200025,
              // 2mm
          objectNumber = 2,
              // 'n' Current object number
          outToPages = !1,
              // switches where out() prints. outToPages true = push to pages obj. outToPages false = doc builder content
          offsets = [],
              // List of offsets. Activated and reset by buildDocument(). Pupulated by various calls buildDocument makes.
          fonts = {},
              // collection of font objects, where key is fontKey - a dynamically created label for a given font.
          fontmap = {},
              // mapping structure fontName > fontStyle > font key - performance layer. See addFont()
          activeFontKey,
              // will be string representing the KEY of the font as combination of fontName + fontStyle
          k,
              // Scale factor
          tmp,
              page = 0,
              currentPage,
              pages = [],
              pagesContext = [],
              // same index as pages and pagedim
          pagedim = [],
              content = [],
              additionalObjects = [],
              lineCapID = 0,
              lineJoinID = 0,
              content_length = 0,
              pageWidth,
              pageHeight,
              pageMode,
              zoomMode,
              layoutMode,
              documentProperties = {
              'title': '',
              'subject': '',
              'author': '',
              'keywords': '',
              'creator': ''
          },
              API = {},
              events = new PubSub(API),


          /////////////////////
          // Private functions
          /////////////////////
          f2 = function f2(number) {
              return number.toFixed(2); // Ie, %.2f
          },
              f3 = function f3(number) {
              return number.toFixed(3); // Ie, %.3f
          },
              padd2 = function padd2(number) {
              return ('0' + parseInt(number)).slice(-2);
          },
              out = function out(string) {
              if (outToPages) {
                  /* set by beginPage */
                  pages[currentPage].push(string);
              } else {
                  // +1 for '\n' that will be used to join 'content'
                  content_length += string.length + 1;
                  content.push(string);
              }
          },
              newObject = function newObject() {
              // Begin a new object
              objectNumber++;
              offsets[objectNumber] = content_length;
              out(objectNumber + ' 0 obj');
              return objectNumber;
          },

          // Does not output the object until after the pages have been output.
          // Returns an object containing the objectId and content.
          // All pages have been added so the object ID can be estimated to start right after.
          // This does not modify the current objectNumber;  It must be updated after the newObjects are output.
          newAdditionalObject = function newAdditionalObject() {
              var objId = pages.length * 2 + 1;
              objId += additionalObjects.length;
              var obj = { objId: objId, content: '' };
              additionalObjects.push(obj);
              return obj;
          },

          // Does not output the object.  The caller must call newObjectDeferredBegin(oid) before outputing any data
          newObjectDeferred = function newObjectDeferred() {
              objectNumber++;
              offsets[objectNumber] = function () {
                  return content_length;
              };
              return objectNumber;
          },
              newObjectDeferredBegin = function newObjectDeferredBegin(oid) {
              offsets[oid] = content_length;
          },
              putStream = function putStream(str) {
              out('stream');
              out(str);
              out('endstream');
          },
              putPages = function putPages() {
              var n,
                  p,
                  arr,
                  i,
                  deflater,
                  adler32,
                  adler32cs,
                  wPt,
                  hPt,
                  pageObjectNumbers = [];

              adler32cs = global.adler32cs || jsPDF.adler32cs;
              if (compress && typeof adler32cs === 'undefined') {
                  compress = false;
              }

              // outToPages = false as set in endDocument(). out() writes to content.

              for (n = 1; n <= page; n++) {
                  pageObjectNumbers.push(newObject());
                  wPt = (pageWidth = pagedim[n].width) * k;
                  hPt = (pageHeight = pagedim[n].height) * k;
                  out('<</Type /Page');
                  out('/Parent 1 0 R');
                  out('/Resources 2 0 R');
                  out('/MediaBox [0 0 ' + f2(wPt) + ' ' + f2(hPt) + ']');
                  // Added for annotation plugin
                  events.publish('putPage', { pageNumber: n, page: pages[n] });
                  out('/Contents ' + (objectNumber + 1) + ' 0 R');
                  out('>>');
                  out('endobj');

                  // Page content
                  p = pages[n].join('\n');
                  newObject();
                  if (compress) {
                      arr = [];
                      i = p.length;
                      while (i--) {
                          arr[i] = p.charCodeAt(i);
                      }
                      adler32 = adler32cs.from(p);
                      deflater = new Deflater(6);
                      deflater.append(new Uint8Array(arr));
                      p = deflater.flush();
                      arr = new Uint8Array(p.length + 6);
                      arr.set(new Uint8Array([120, 156])), arr.set(p, 2);
                      arr.set(new Uint8Array([adler32 & 0xFF, adler32 >> 8 & 0xFF, adler32 >> 16 & 0xFF, adler32 >> 24 & 0xFF]), p.length + 2);
                      p = String.fromCharCode.apply(null, arr);
                      out('<</Length ' + p.length + ' /Filter [/FlateDecode]>>');
                  } else {
                      out('<</Length ' + p.length + '>>');
                  }
                  putStream(p);
                  out('endobj');
              }
              offsets[1] = content_length;
              out('1 0 obj');
              out('<</Type /Pages');
              var kids = '/Kids [';
              for (i = 0; i < page; i++) {
                  kids += pageObjectNumbers[i] + ' 0 R ';
              }
              out(kids + ']');
              out('/Count ' + page);
              out('>>');
              out('endobj');
              events.publish('postPutPages');
          },
              putFont = function putFont(font) {
              font.objectNumber = newObject();
              out('<</BaseFont/' + font.PostScriptName + '/Type/Font');
              if (typeof font.encoding === 'string') {
                  out('/Encoding/' + font.encoding);
              }
              out('/Subtype/Type1>>');
              out('endobj');
          },
              putFonts = function putFonts() {
              for (var fontKey in fonts) {
                  if (fonts.hasOwnProperty(fontKey)) {
                      putFont(fonts[fontKey]);
                  }
              }
          },
              putXobjectDict = function putXobjectDict() {
              // Loop through images, or other data objects
              events.publish('putXobjectDict');
          },
              putResourceDictionary = function putResourceDictionary() {
              out('/ProcSet [/PDF /Text /ImageB /ImageC /ImageI]');
              out('/Font <<');

              // Do this for each font, the '1' bit is the index of the font
              for (var fontKey in fonts) {
                  if (fonts.hasOwnProperty(fontKey)) {
                      out('/' + fontKey + ' ' + fonts[fontKey].objectNumber + ' 0 R');
                  }
              }
              out('>>');
              out('/XObject <<');
              putXobjectDict();
              out('>>');
          },
              putResources = function putResources() {
              putFonts();
              events.publish('putResources');
              // Resource dictionary
              offsets[2] = content_length;
              out('2 0 obj');
              out('<<');
              putResourceDictionary();
              out('>>');
              out('endobj');
              events.publish('postPutResources');
          },
              putAdditionalObjects = function putAdditionalObjects() {
              events.publish('putAdditionalObjects');
              for (var i = 0; i < additionalObjects.length; i++) {
                  var obj = additionalObjects[i];
                  offsets[obj.objId] = content_length;
                  out(obj.objId + ' 0 obj');
                  out(obj.content);
                  ;
                  out('endobj');
              }
              objectNumber += additionalObjects.length;
              events.publish('postPutAdditionalObjects');
          },
              addToFontDictionary = function addToFontDictionary(fontKey, fontName, fontStyle) {
              // this is mapping structure for quick font key lookup.
              // returns the KEY of the font (ex: "F1") for a given
              // pair of font name and type (ex: "Arial". "Italic")
              if (!fontmap.hasOwnProperty(fontName)) {
                  fontmap[fontName] = {};
              }
              fontmap[fontName][fontStyle] = fontKey;
          },

          /**
           * FontObject describes a particular font as member of an instnace of jsPDF
           *
           * It's a collection of properties like 'id' (to be used in PDF stream),
           * 'fontName' (font's family name), 'fontStyle' (font's style variant label)
           *
           * @class
           * @public
           * @property id {String} PDF-document-instance-specific label assinged to the font.
           * @property PostScriptName {String} PDF specification full name for the font
           * @property encoding {Object} Encoding_name-to-Font_metrics_object mapping.
           * @name FontObject
           */
          addFont = function addFont(PostScriptName, fontName, fontStyle, encoding) {
              var fontKey = 'F' + (Object.keys(fonts).length + 1).toString(10),

              // This is FontObject
              font = fonts[fontKey] = {
                  'id': fontKey,
                  'PostScriptName': PostScriptName,
                  'fontName': fontName,
                  'fontStyle': fontStyle,
                  'encoding': encoding,
                  'metadata': {}
              };
              addToFontDictionary(fontKey, fontName, fontStyle);
              events.publish('addFont', font);

              return fontKey;
          },
              addFonts = function addFonts() {

              var HELVETICA = "helvetica",
                  TIMES = "times",
                  COURIER = "courier",
                  NORMAL = "normal",
                  BOLD = "bold",
                  ITALIC = "italic",
                  BOLD_ITALIC = "bolditalic",
                  encoding = 'StandardEncoding',
                  ZAPF = "zapfdingbats",
                  standardFonts = [['Helvetica', HELVETICA, NORMAL], ['Helvetica-Bold', HELVETICA, BOLD], ['Helvetica-Oblique', HELVETICA, ITALIC], ['Helvetica-BoldOblique', HELVETICA, BOLD_ITALIC], ['Courier', COURIER, NORMAL], ['Courier-Bold', COURIER, BOLD], ['Courier-Oblique', COURIER, ITALIC], ['Courier-BoldOblique', COURIER, BOLD_ITALIC], ['Times-Roman', TIMES, NORMAL], ['Times-Bold', TIMES, BOLD], ['Times-Italic', TIMES, ITALIC], ['Times-BoldItalic', TIMES, BOLD_ITALIC], ['ZapfDingbats', ZAPF]];

              for (var i = 0, l = standardFonts.length; i < l; i++) {
                  var fontKey = addFont(standardFonts[i][0], standardFonts[i][1], standardFonts[i][2], encoding);

                  // adding aliases for standard fonts, this time matching the capitalization
                  var parts = standardFonts[i][0].split('-');
                  addToFontDictionary(fontKey, parts[0], parts[1] || '');
              }
              events.publish('addFonts', { fonts: fonts, dictionary: fontmap });
          },
              SAFE = function __safeCall(fn) {
              fn.foo = function __safeCallWrapper() {
                  try {
                      return fn.apply(this, arguments);
                  } catch (e) {
                      var stack = e.stack || '';
                      if (~stack.indexOf(' at ')) stack = stack.split(" at ")[1];
                      var m = "Error in function " + stack.split("\n")[0].split('<')[0] + ": " + e.message;
                      if (global.console) {
                          global.console.error(m, e);
                          if (global.alert) alert(m);
                      } else {
                          throw new Error(m);
                      }
                  }
              };
              fn.foo.bar = fn;
              return fn.foo;
          },
              to8bitStream = function to8bitStream(text, flags) {
              /**
               * PDF 1.3 spec:
               * "For text strings encoded in Unicode, the first two bytes must be 254 followed by
               * 255, representing the Unicode byte order marker, U+FEFF. (This sequence conflicts
               * with the PDFDocEncoding character sequence thorn ydieresis, which is unlikely
               * to be a meaningful beginning of a word or phrase.) The remainder of the
               * string consists of Unicode character codes, according to the UTF-16 encoding
               * specified in the Unicode standard, version 2.0. Commonly used Unicode values
               * are represented as 2 bytes per character, with the high-order byte appearing first
               * in the string."
               *
               * In other words, if there are chars in a string with char code above 255, we
               * recode the string to UCS2 BE - string doubles in length and BOM is prepended.
               *
               * HOWEVER!
               * Actual *content* (body) text (as opposed to strings used in document properties etc)
               * does NOT expect BOM. There, it is treated as a literal GID (Glyph ID)
               *
               * Because of Adobe's focus on "you subset your fonts!" you are not supposed to have
               * a font that maps directly Unicode (UCS2 / UTF16BE) code to font GID, but you could
               * fudge it with "Identity-H" encoding and custom CIDtoGID map that mimics Unicode
               * code page. There, however, all characters in the stream are treated as GIDs,
               * including BOM, which is the reason we need to skip BOM in content text (i.e. that
               * that is tied to a font).
               *
               * To signal this "special" PDFEscape / to8bitStream handling mode,
               * API.text() function sets (unless you overwrite it with manual values
               * given to API.text(.., flags) )
               * flags.autoencode = true
               * flags.noBOM = true
               *
               * ===================================================================================
               * `flags` properties relied upon:
               *   .sourceEncoding = string with encoding label.
               *                     "Unicode" by default. = encoding of the incoming text.
               *                     pass some non-existing encoding name
               *                     (ex: 'Do not touch my strings! I know what I am doing.')
               *                     to make encoding code skip the encoding step.
               *   .outputEncoding = Either valid PDF encoding name
               *                     (must be supported by jsPDF font metrics, otherwise no encoding)
               *                     or a JS object, where key = sourceCharCode, value = outputCharCode
               *                     missing keys will be treated as: sourceCharCode === outputCharCode
               *   .noBOM
               *       See comment higher above for explanation for why this is important
               *   .autoencode
               *       See comment higher above for explanation for why this is important
               */

              var i, l, sourceEncoding, encodingBlock, outputEncoding, newtext, isUnicode, ch, bch;

              flags = flags || {};
              sourceEncoding = flags.sourceEncoding || 'Unicode';
              outputEncoding = flags.outputEncoding;

              // This 'encoding' section relies on font metrics format
              // attached to font objects by, among others,
              // "Willow Systems' standard_font_metrics plugin"
              // see jspdf.plugin.standard_font_metrics.js for format
              // of the font.metadata.encoding Object.
              // It should be something like
              //   .encoding = {'codePages':['WinANSI....'], 'WinANSI...':{code:code, ...}}
              //   .widths = {0:width, code:width, ..., 'fof':divisor}
              //   .kerning = {code:{previous_char_code:shift, ..., 'fof':-divisor},...}
              if ((flags.autoencode || outputEncoding) && fonts[activeFontKey].metadata && fonts[activeFontKey].metadata[sourceEncoding] && fonts[activeFontKey].metadata[sourceEncoding].encoding) {
                  encodingBlock = fonts[activeFontKey].metadata[sourceEncoding].encoding;

                  // each font has default encoding. Some have it clearly defined.
                  if (!outputEncoding && fonts[activeFontKey].encoding) {
                      outputEncoding = fonts[activeFontKey].encoding;
                  }

                  // Hmmm, the above did not work? Let's try again, in different place.
                  if (!outputEncoding && encodingBlock.codePages) {
                      outputEncoding = encodingBlock.codePages[0]; // let's say, first one is the default
                  }

                  if (typeof outputEncoding === 'string') {
                      outputEncoding = encodingBlock[outputEncoding];
                  }
                  // we want output encoding to be a JS Object, where
                  // key = sourceEncoding's character code and
                  // value = outputEncoding's character code.
                  if (outputEncoding) {
                      isUnicode = false;
                      newtext = [];
                      for (i = 0, l = text.length; i < l; i++) {
                          ch = outputEncoding[text.charCodeAt(i)];
                          if (ch) {
                              newtext.push(String.fromCharCode(ch));
                          } else {
                              newtext.push(text[i]);
                          }

                          // since we are looping over chars anyway, might as well
                          // check for residual unicodeness
                          if (newtext[i].charCodeAt(0) >> 8) {
                              /* more than 255 */
                              isUnicode = true;
                          }
                      }
                      text = newtext.join('');
                  }
              }

              i = text.length;
              // isUnicode may be set to false above. Hence the triple-equal to undefined
              while (isUnicode === undefined && i !== 0) {
                  if (text.charCodeAt(i - 1) >> 8) {
                      /* more than 255 */
                      isUnicode = true;
                  }
                  i--;
              }
              if (!isUnicode) {
                  return text;
              }

              newtext = flags.noBOM ? [] : [254, 255];
              for (i = 0, l = text.length; i < l; i++) {
                  ch = text.charCodeAt(i);
                  bch = ch >> 8; // divide by 256
                  if (bch >> 8) {
                      /* something left after dividing by 256 second time */
                      throw new Error("Character at position " + i + " of string '" + text + "' exceeds 16bits. Cannot be encoded into UCS-2 BE");
                  }
                  newtext.push(bch);
                  newtext.push(ch - (bch << 8));
              }
              return String.fromCharCode.apply(undefined, newtext);
          },
              pdfEscape = function pdfEscape(text, flags) {
              /**
               * Replace '/', '(', and ')' with pdf-safe versions
               *
               * Doing to8bitStream does NOT make this PDF display unicode text. For that
               * we also need to reference a unicode font and embed it - royal pain in the rear.
               *
               * There is still a benefit to to8bitStream - PDF simply cannot handle 16bit chars,
               * which JavaScript Strings are happy to provide. So, while we still cannot display
               * 2-byte characters property, at least CONDITIONALLY converting (entire string containing)
               * 16bit chars to (USC-2-BE) 2-bytes per char + BOM streams we ensure that entire PDF
               * is still parseable.
               * This will allow immediate support for unicode in document properties strings.
               */
              return to8bitStream(text, flags).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
          },
              putInfo = function putInfo() {
              out('/Producer (jsPDF ' + jsPDF.version + ')');
              for (var key in documentProperties) {
                  if (documentProperties.hasOwnProperty(key) && documentProperties[key]) {
                      out('/' + key.substr(0, 1).toUpperCase() + key.substr(1) + ' (' + pdfEscape(documentProperties[key]) + ')');
                  }
              }
              var created = new Date(),
                  tzoffset = created.getTimezoneOffset(),
                  tzsign = tzoffset < 0 ? '+' : '-',
                  tzhour = Math.floor(Math.abs(tzoffset / 60)),
                  tzmin = Math.abs(tzoffset % 60),
                  tzstr = [tzsign, padd2(tzhour), "'", padd2(tzmin), "'"].join('');
              out(['/CreationDate (D:', created.getFullYear(), padd2(created.getMonth() + 1), padd2(created.getDate()), padd2(created.getHours()), padd2(created.getMinutes()), padd2(created.getSeconds()), tzstr, ')'].join(''));
          },
              putCatalog = function putCatalog() {
              out('/Type /Catalog');
              out('/Pages 1 0 R');
              // PDF13ref Section 7.2.1
              if (!zoomMode) zoomMode = 'fullwidth';
              switch (zoomMode) {
                  case 'fullwidth':
                      out('/OpenAction [3 0 R /FitH null]');
                      break;
                  case 'fullheight':
                      out('/OpenAction [3 0 R /FitV null]');
                      break;
                  case 'fullpage':
                      out('/OpenAction [3 0 R /Fit]');
                      break;
                  case 'original':
                      out('/OpenAction [3 0 R /XYZ null null 1]');
                      break;
                  default:
                      var pcn = '' + zoomMode;
                      if (pcn.substr(pcn.length - 1) === '%') zoomMode = parseInt(zoomMode) / 100;
                      if (typeof zoomMode === 'number') {
                          out('/OpenAction [3 0 R /XYZ null null ' + f2(zoomMode) + ']');
                      }
              }
              if (!layoutMode) layoutMode = 'continuous';
              switch (layoutMode) {
                  case 'continuous':
                      out('/PageLayout /OneColumn');
                      break;
                  case 'single':
                      out('/PageLayout /SinglePage');
                      break;
                  case 'two':
                  case 'twoleft':
                      out('/PageLayout /TwoColumnLeft');
                      break;
                  case 'tworight':
                      out('/PageLayout /TwoColumnRight');
                      break;
              }
              if (pageMode) {
                  /**
                   * A name object specifying how the document should be displayed when opened:
                   * UseNone      : Neither document outline nor thumbnail images visible -- DEFAULT
                   * UseOutlines  : Document outline visible
                   * UseThumbs    : Thumbnail images visible
                   * FullScreen   : Full-screen mode, with no menu bar, window controls, or any other window visible
                   */
                  out('/PageMode /' + pageMode);
              }
              events.publish('putCatalog');
          },
              putTrailer = function putTrailer() {
              out('/Size ' + (objectNumber + 1));
              out('/Root ' + objectNumber + ' 0 R');
              out('/Info ' + (objectNumber - 1) + ' 0 R');
          },
              beginPage = function beginPage(width, height) {
              // Dimensions are stored as user units and converted to points on output
              var orientation = typeof height === 'string' && height.toLowerCase();
              if (typeof width === 'string') {
                  var format = width.toLowerCase();
                  if (pageFormats.hasOwnProperty(format)) {
                      width = pageFormats[format][0] / k;
                      height = pageFormats[format][1] / k;
                  }
              }
              if (Array.isArray(width)) {
                  height = width[1];
                  width = width[0];
              }
              if (orientation) {
                  switch (orientation.substr(0, 1)) {
                      case 'l':
                          if (height > width) orientation = 's';
                          break;
                      case 'p':
                          if (width > height) orientation = 's';
                          break;
                  }
                  if (orientation === 's') {
                      tmp = width;
                      width = height;
                      height = tmp;
                  }
              }
              outToPages = true;
              pages[++page] = [];
              pagedim[page] = {
                  width: Number(width) || pageWidth,
                  height: Number(height) || pageHeight
              };
              pagesContext[page] = {};
              _setPage(page);
          },
              _addPage = function _addPage() {
              beginPage.apply(this, arguments);
              // Set line width
              out(f2(lineWidth * k) + ' w');
              // Set draw color
              out(drawColor);
              // resurrecting non-default line caps, joins
              if (lineCapID !== 0) {
                  out(lineCapID + ' J');
              }
              if (lineJoinID !== 0) {
                  out(lineJoinID + ' j');
              }
              events.publish('addPage', { pageNumber: page });
          },
              _deletePage = function _deletePage(n) {
              if (n > 0 && n <= page) {
                  pages.splice(n, 1);
                  pagedim.splice(n, 1);
                  page--;
                  if (currentPage > page) {
                      currentPage = page;
                  }
                  this.setPage(currentPage);
              }
          },
              _setPage = function _setPage(n) {
              if (n > 0 && n <= page) {
                  currentPage = n;
                  pageWidth = pagedim[n].width;
                  pageHeight = pagedim[n].height;
              }
          },

          /**
           * Returns a document-specific font key - a label assigned to a
           * font name + font type combination at the time the font was added
           * to the font inventory.
           *
           * Font key is used as label for the desired font for a block of text
           * to be added to the PDF document stream.
           * @private
           * @function
           * @param fontName {String} can be undefined on "falthy" to indicate "use current"
           * @param fontStyle {String} can be undefined on "falthy" to indicate "use current"
           * @returns {String} Font key.
           */
          _getFont = function _getFont(fontName, fontStyle) {
              var key;

              fontName = fontName !== undefined ? fontName : fonts[activeFontKey].fontName;
              fontStyle = fontStyle !== undefined ? fontStyle : fonts[activeFontKey].fontStyle;

              if (fontName !== undefined) {
                  fontName = fontName.toLowerCase();
              }
              switch (fontName) {
                  case 'sans-serif':
                  case 'verdana':
                  case 'arial':
                  case 'helvetica':
                      fontName = 'helvetica';
                      break;
                  case 'fixed':
                  case 'monospace':
                  case 'terminal':
                  case 'courier':
                      fontName = 'courier';
                      break;
                  case 'serif':
                  case 'cursive':
                  case 'fantasy':
                  default:
                      fontName = 'times';
                      break;
              }

              try {
                  // get a string like 'F3' - the KEY corresponding tot he font + type combination.
                  key = fontmap[fontName][fontStyle];
              } catch (e) {}

              if (!key) {
                  //throw new Error("Unable to look up font label for font '" + fontName + "', '"
                  //+ fontStyle + "'. Refer to getFontList() for available fonts.");
                  key = fontmap['times'][fontStyle];
                  if (key == null) {
                      key = fontmap['times']['normal'];
                  }
              }
              return key;
          },
              buildDocument = function buildDocument() {
              outToPages = false; // switches out() to content

              objectNumber = 2;
              content_length = 0;
              content = [];
              offsets = [];
              additionalObjects = [];
              // Added for AcroForm
              events.publish('buildDocument');

              // putHeader()
              out('%PDF-' + pdfVersion);

              putPages();

              // Must happen after putPages
              // Modifies current object Id
              putAdditionalObjects();

              putResources();

              // Info
              newObject();
              out('<<');
              putInfo();
              out('>>');
              out('endobj');

              // Catalog
              newObject();
              out('<<');
              putCatalog();
              out('>>');
              out('endobj');

              // Cross-ref
              var o = content_length,
                  i,
                  p = "0000000000";
              out('xref');
              out('0 ' + (objectNumber + 1));
              out(p + ' 65535 f ');
              for (i = 1; i <= objectNumber; i++) {
                  var offset = offsets[i];
                  if (typeof offset === 'function') {
                      out((p + offsets[i]()).slice(-10) + ' 00000 n ');
                  } else {
                      out((p + offsets[i]).slice(-10) + ' 00000 n ');
                  }
              }
              // Trailer
              out('trailer');
              out('<<');
              putTrailer();
              out('>>');
              out('startxref');
              out('' + o);
              out('%%EOF');

              outToPages = true;

              return content.join('\n');
          },
              getStyle = function getStyle(style) {
              // see path-painting operators in PDF spec
              var op = 'S'; // stroke
              if (style === 'F') {
                  op = 'f'; // fill
              } else if (style === 'FD' || style === 'DF') {
                  op = 'B'; // both
              } else if (style === 'f' || style === 'f*' || style === 'B' || style === 'B*') {
                  /*
                   Allow direct use of these PDF path-painting operators:
                   - f	fill using nonzero winding number rule
                   - f*	fill using even-odd rule
                   - B	fill then stroke with fill using non-zero winding number rule
                   - B*	fill then stroke with fill using even-odd rule
                   */
                  op = style;
              }
              return op;
          },
              getArrayBuffer = function getArrayBuffer() {
              var data = buildDocument(),
                  len = data.length,
                  ab = new ArrayBuffer(len),
                  u8 = new Uint8Array(ab);

              while (len--) {
                  u8[len] = data.charCodeAt(len);
              }return ab;
          },
              getBlob = function getBlob() {
              return new Blob([getArrayBuffer()], { type: "application/pdf" });
          },

          /**
           * Generates the PDF document.
           *
           * If `type` argument is undefined, output is raw body of resulting PDF returned as a string.
           *
           * @param {String} type A string identifying one of the possible output types.
           * @param {Object} options An object providing some additional signalling to PDF generator.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name output
           */
          _output = SAFE(function (type, options) {
              var datauri = ('' + type).substr(0, 6) === 'dataur' ? 'data:application/pdf;base64,' + btoa(buildDocument()) : 0;

              switch (type) {
                  case undefined:
                      return buildDocument();
                  case 'save':
                      if (navigator.getUserMedia) {
                          if (global.URL === undefined || global.URL.createObjectURL === undefined) {
                              return API.output('dataurlnewwindow');
                          }
                      }
                      saveAs(getBlob(), options);
                      if (typeof saveAs.unload === 'function') {
                          if (global.setTimeout) {
                              setTimeout(saveAs.unload, 911);
                          }
                      }
                      break;
                  case 'arraybuffer':
                      return getArrayBuffer();
                  case 'blob':
                      return getBlob();
                  case 'bloburi':
                  case 'bloburl':
                      // User is responsible of calling revokeObjectURL
                      return global.URL && global.URL.createObjectURL(getBlob()) || void 0;
                  case 'datauristring':
                  case 'dataurlstring':
                      return datauri;
                  case 'dataurlnewwindow':
                      var nW = global.open(datauri);
                      if (nW || typeof safari === "undefined") return nW;
                  /* pass through */
                  case 'datauri':
                  case 'dataurl':
                      return global.document.location.href = datauri;
                  default:
                      throw new Error('Output type "' + type + '" is not supported.');
              }
              // @TODO: Add different output options
          });

          switch (unit) {
              case 'pt':
                  k = 1;
                  break;
              case 'mm':
                  k = 72 / 25.4000508;
                  break;
              case 'cm':
                  k = 72 / 2.54000508;
                  break;
              case 'in':
                  k = 72;
                  break;
              case 'px':
                  k = 96 / 72;
                  break;
              case 'pc':
                  k = 12;
                  break;
              case 'em':
                  k = 12;
                  break;
              case 'ex':
                  k = 6;
                  break;
              default:
                  throw 'Invalid unit: ' + unit;
          }

          //---------------------------------------
          // Public API

          /**
           * Object exposing internal API to plugins
           * @public
           */
          API.internal = {
              'pdfEscape': pdfEscape,
              'getStyle': getStyle,
              /**
               * Returns {FontObject} describing a particular font.
               * @public
               * @function
               * @param fontName {String} (Optional) Font's family name
               * @param fontStyle {String} (Optional) Font's style variation name (Example:"Italic")
               * @returns {FontObject}
               */
              'getFont': function getFont() {
                  return fonts[_getFont.apply(API, arguments)];
              },
              'getFontSize': function getFontSize() {
                  return activeFontSize;
              },
              'getLineHeight': function getLineHeight() {
                  return activeFontSize * lineHeightProportion;
              },
              'write': function write(string1 /*, string2, string3, etc */) {
                  out(arguments.length === 1 ? string1 : Array.prototype.join.call(arguments, ' '));
              },
              'getCoordinateString': function getCoordinateString(value) {
                  return f2(value * k);
              },
              'getVerticalCoordinateString': function getVerticalCoordinateString(value) {
                  return f2((pageHeight - value) * k);
              },
              'collections': {},
              'newObject': newObject,
              'newAdditionalObject': newAdditionalObject,
              'newObjectDeferred': newObjectDeferred,
              'newObjectDeferredBegin': newObjectDeferredBegin,
              'putStream': putStream,
              'events': events,
              // ratio that you use in multiplication of a given "size" number to arrive to 'point'
              // units of measurement.
              // scaleFactor is set at initialization of the document and calculated against the stated
              // default measurement units for the document.
              // If default is "mm", k is the number that will turn number in 'mm' into 'points' number.
              // through multiplication.
              'scaleFactor': k,
              'pageSize': {
                  get width() {
                      return pageWidth;
                  },
                  get height() {
                      return pageHeight;
                  }
              },
              'output': function output(type, options) {
                  return _output(type, options);
              },
              'getNumberOfPages': function getNumberOfPages() {
                  return pages.length - 1;
              },
              'pages': pages,
              'out': out,
              'f2': f2,
              'getPageInfo': function getPageInfo(pageNumberOneBased) {
                  var objId = (pageNumberOneBased - 1) * 2 + 3;
                  return { objId: objId, pageNumber: pageNumberOneBased, pageContext: pagesContext[pageNumberOneBased] };
              },
              'getCurrentPageInfo': function getCurrentPageInfo() {
                  var objId = (currentPage - 1) * 2 + 3;
                  return { objId: objId, pageNumber: currentPage, pageContext: pagesContext[currentPage] };
              },
              'getPDFVersion': function getPDFVersion() {
                  return pdfVersion;
              }
          };

          /**
           * Adds (and transfers the focus to) new page to the PDF document.
           * @function
           * @returns {jsPDF}
           *
           * @methodOf jsPDF#
           * @name addPage
           */
          API.addPage = function () {
              _addPage.apply(this, arguments);
              return this;
          };
          API.setPage = function () {
              _setPage.apply(this, arguments);
              return this;
          };
          API.insertPage = function (beforePage) {
              this.addPage();
              this.movePage(currentPage, beforePage);
              return this;
          };
          API.movePage = function (targetPage, beforePage) {
              if (targetPage > beforePage) {
                  var tmpPages = pages[targetPage];
                  var tmpPagedim = pagedim[targetPage];
                  var tmpPagesContext = pagesContext[targetPage];
                  for (var i = targetPage; i > beforePage; i--) {
                      pages[i] = pages[i - 1];
                      pagedim[i] = pagedim[i - 1];
                      pagesContext[i] = pagesContext[i - 1];
                  }
                  pages[beforePage] = tmpPages;
                  pagedim[beforePage] = tmpPagedim;
                  pagesContext[beforePage] = tmpPagesContext;
                  this.setPage(beforePage);
              } else if (targetPage < beforePage) {
                  var tmpPages = pages[targetPage];
                  var tmpPagedim = pagedim[targetPage];
                  var tmpPagesContext = pagesContext[targetPage];
                  for (var i = targetPage; i < beforePage; i++) {
                      pages[i] = pages[i + 1];
                      pagedim[i] = pagedim[i + 1];
                      pagesContext[i] = pagesContext[i + 1];
                  }
                  pages[beforePage] = tmpPages;
                  pagedim[beforePage] = tmpPagedim;
                  pagesContext[beforePage] = tmpPagesContext;
                  this.setPage(beforePage);
              }
              return this;
          };

          API.deletePage = function () {
              _deletePage.apply(this, arguments);
              return this;
          };
          API.setDisplayMode = function (zoom, layout, pmode) {
              zoomMode = zoom;
              layoutMode = layout;
              pageMode = pmode;
              return this;
          },

          /**
           * Adds text to page. Supports adding multiline text when 'text' argument is an Array of Strings.
           *
           * @function
           * @param {String|Array} text String or array of strings to be added to the page. Each line is shifted one line down per font, spacing settings declared before this call.
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Object} flags Collection of settings signalling how the text must be encoded. Defaults are sane. If you think you want to pass some flags, you likely can read the source.
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name text
           */
          API.text = function (text, x, y, flags, angle, align) {
              /**
               * Inserts something like this into PDF
               *   BT
               *    /F1 16 Tf  % Font name + size
               *    16 TL % How many units down for next line in multiline text
               *    0 g % color
               *    28.35 813.54 Td % position
               *    (line one) Tj
               *    T* (line two) Tj
               *    T* (line three) Tj
               *   ET
               */
              function ESC(s) {
                  s = s.split("\t").join(Array(options.TabLen || 9).join(" "));
                  return pdfEscape(s, flags);
              }

              // Pre-August-2012 the order of arguments was function(x, y, text, flags)
              // in effort to make all calls have similar signature like
              //   function(data, coordinates... , miscellaneous)
              // this method had its args flipped.
              // code below allows backward compatibility with old arg order.
              if (typeof text === 'number') {
                  tmp = y;
                  y = x;
                  x = text;
                  text = tmp;
              }

              // If there are any newlines in text, we assume
              // the user wanted to print multiple lines, so break the
              // text up into an array.  If the text is already an array,
              // we assume the user knows what they are doing.
              // Convert text into an array anyway to simplify
              // later code.
              if (typeof text === 'string') {
                  if (text.match(/[\n\r]/)) {
                      text = text.split(/\r\n|\r|\n/g);
                  } else {
                      text = [text];
                  }
              }
              if (typeof angle === 'string') {
                  align = angle;
                  angle = null;
              }
              if (typeof flags === 'string') {
                  align = flags;
                  flags = null;
              }
              if (typeof flags === 'number') {
                  angle = flags;
                  flags = null;
              }
              var xtra = '',
                  mode = 'Td',
                  todo;
              if (angle) {
                  angle *= Math.PI / 180;
                  var c = Math.cos(angle),
                      s = Math.sin(angle);
                  xtra = [f2(c), f2(s), f2(s * -1), f2(c), ''].join(" ");
                  mode = 'Tm';
              }
              flags = flags || {};
              if (!('noBOM' in flags)) flags.noBOM = true;
              if (!('autoencode' in flags)) flags.autoencode = true;

              var strokeOption = '';
              var pageContext = this.internal.getCurrentPageInfo().pageContext;
              if (true === flags.stroke) {
                  if (pageContext.lastTextWasStroke !== true) {
                      strokeOption = '1 Tr\n';
                      pageContext.lastTextWasStroke = true;
                  }
              } else {
                  if (pageContext.lastTextWasStroke) {
                      strokeOption = '0 Tr\n';
                  }
                  pageContext.lastTextWasStroke = false;
              }

              if (typeof this._runningPageHeight === 'undefined') {
                  this._runningPageHeight = 0;
              }

              if (typeof text === 'string') {
                  text = ESC(text);
              } else if (Object.prototype.toString.call(text) === '[object Array]') {
                  // we don't want to destroy  original text array, so cloning it
                  var sa = text.concat(),
                      da = [],
                      len = sa.length;
                  // we do array.join('text that must not be PDFescaped")
                  // thus, pdfEscape each component separately
                  while (len--) {
                      da.push(ESC(sa.shift()));
                  }
                  var linesLeft = Math.ceil((pageHeight - y - this._runningPageHeight) * k / (activeFontSize * lineHeightProportion));
                  if (0 <= linesLeft && linesLeft < da.length + 1) {
                      //todo = da.splice(linesLeft-1);
                  }

                  if (align) {
                      var left,
                          prevX,
                          maxLineLength,
                          leading = activeFontSize * lineHeightProportion,
                          lineWidths = text.map(function (v) {
                          return this.getStringUnitWidth(v) * activeFontSize / k;
                      }, this);
                      maxLineLength = Math.max.apply(Math, lineWidths);
                      // The first line uses the "main" Td setting,
                      // and the subsequent lines are offset by the
                      // previous line's x coordinate.
                      if (align === "center") {
                          // The passed in x coordinate defines
                          // the center point.
                          left = x - maxLineLength / 2;
                          x -= lineWidths[0] / 2;
                      } else if (align === "right") {
                          // The passed in x coordinate defines the
                          // rightmost point of the text.
                          left = x - maxLineLength;
                          x -= lineWidths[0];
                      } else {
                          throw new Error('Unrecognized alignment option, use "center" or "right".');
                      }
                      prevX = x;
                      text = da[0];
                      for (var i = 1, len = da.length; i < len; i++) {
                          var delta = maxLineLength - lineWidths[i];
                          if (align === "center") delta /= 2;
                          // T* = x-offset leading Td ( text )
                          text += ") Tj\n" + (left - prevX + delta) + " -" + leading + " Td (" + da[i];
                          prevX = left + delta;
                      }
                  } else {
                      text = da.join(") Tj\nT* (");
                  }
              } else {
                  throw new Error('Type of text must be string or Array. "' + text + '" is not recognized.');
              }
              // Using "'" ("go next line and render text" mark) would save space but would complicate our rendering code, templates

              // BT .. ET does NOT have default settings for Tf. You must state that explicitely every time for BT .. ET
              // if you want text transformation matrix (+ multiline) to work reliably (which reads sizes of things from font declarations)
              // Thus, there is NO useful, *reliable* concept of "default" font for a page.
              // The fact that "default" (reuse font used before) font worked before in basic cases is an accident
              // - readers dealing smartly with brokenness of jsPDF's markup.

              var curY;

              if (todo) {
                  //this.addPage();
                  //this._runningPageHeight += y -  (activeFontSize * 1.7 / k);
                  //curY = f2(pageHeight - activeFontSize * 1.7 /k);
              } else {
                  curY = f2((pageHeight - y) * k);
              }
              //curY = f2((pageHeight - (y - this._runningPageHeight)) * k);

              //			if (curY < 0){
              //				console.log('auto page break');
              //				this.addPage();
              //				this._runningPageHeight = y -  (activeFontSize * 1.7 / k);
              //				curY = f2(pageHeight - activeFontSize * 1.7 /k);
              //			}

              out('BT\n/' + activeFontKey + ' ' + activeFontSize + ' Tf\n' + // font face, style, size
              activeFontSize * lineHeightProportion + ' TL\n' + // line spacing
              strokeOption + // stroke option
              textColor + '\n' + xtra + f2(x * k) + ' ' + curY + ' ' + mode + '\n(' + text + ') Tj\nET');

              if (todo) {
                  //this.text( todo, x, activeFontSize * 1.7 / k);
                  //this.text( todo, x, this._runningPageHeight + (activeFontSize * 1.7 / k));
                  this.text(todo, x, y); // + (activeFontSize * 1.7 / k));
              }

              return this;
          };

          API.lstext = function (text, x, y, spacing) {
              for (var i = 0, len = text.length; i < len; i++, x += spacing) {
                  this.text(text[i], x, y);
              }
          };

          API.line = function (x1, y1, x2, y2) {
              return this.lines([[x2 - x1, y2 - y1]], x1, y1);
          };

          API.clip = function () {
              // By patrick-roberts, github.com/MrRio/jsPDF/issues/328
              // Call .clip() after calling .rect() with a style argument of null
              out('W'); // clip
              out('S'); // stroke path; necessary for clip to work
          };

          /**
           * This fixes the previous function clip(). Perhaps the 'stroke path' hack was due to the missing 'n' instruction?
           * We introduce the fixed version so as to not break API.
           * @param fillRule
           */
          API.clip_fixed = function (fillRule) {
              // Call .clip() after calling drawing ops with a style argument of null
              // W is the PDF clipping op
              if ('evenodd' === fillRule) {
                  out('W*');
              } else {
                  out('W');
              }
              // End the path object without filling or stroking it.
              // This operator is a path-painting no-op, used primarily for the side effect of changing the current clipping path
              // (see Section 4.4.3, “Clipping Path Operators”)
              out('n');
          };

          /**
           * Adds series of curves (straight lines or cubic bezier curves) to canvas, starting at `x`, `y` coordinates.
           * All data points in `lines` are relative to last line origin.
           * `x`, `y` become x1,y1 for first line / curve in the set.
           * For lines you only need to specify [x2, y2] - (ending point) vector against x1, y1 starting point.
           * For bezier curves you need to specify [x2,y2,x3,y3,x4,y4] - vectors to control points 1, 2, ending point. All vectors are against the start of the curve - x1,y1.
           *
           * @example .lines([[2,2],[-2,2],[1,1,2,2,3,3],[2,1]], 212,110, 10) // line, line, bezier curve, line
           * @param {Array} lines Array of *vector* shifts as pairs (lines) or sextets (cubic bezier curves).
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} scale (Defaults to [1.0,1.0]) x,y Scaling factor for all vectors. Elements can be any floating number Sub-one makes drawing smaller. Over-one grows the drawing. Negative flips the direction.
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @param {Boolean} closed If true, the path is closed with a straight line from the end of the last curve to the starting point.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name lines
           */
          API.lines = function (lines, x, y, scale, style, closed) {
              var scalex, scaley, i, l, leg, x2, y2, x3, y3, x4, y4;

              // Pre-August-2012 the order of arguments was function(x, y, lines, scale, style)
              // in effort to make all calls have similar signature like
              //   function(content, coordinateX, coordinateY , miscellaneous)
              // this method had its args flipped.
              // code below allows backward compatibility with old arg order.
              if (typeof lines === 'number') {
                  tmp = y;
                  y = x;
                  x = lines;
                  lines = tmp;
              }

              scale = scale || [1, 1];

              // starting point
              out(f3(x * k) + ' ' + f3((pageHeight - y) * k) + ' m ');

              scalex = scale[0];
              scaley = scale[1];
              l = lines.length;
              //, x2, y2 // bezier only. In page default measurement "units", *after* scaling
              //, x3, y3 // bezier only. In page default measurement "units", *after* scaling
              // ending point for all, lines and bezier. . In page default measurement "units", *after* scaling
              x4 = x; // last / ending point = starting point for first item.
              y4 = y; // last / ending point = starting point for first item.

              for (i = 0; i < l; i++) {
                  leg = lines[i];
                  if (leg.length === 2) {
                      // simple line
                      x4 = leg[0] * scalex + x4; // here last x4 was prior ending point
                      y4 = leg[1] * scaley + y4; // here last y4 was prior ending point
                      out(f3(x4 * k) + ' ' + f3((pageHeight - y4) * k) + ' l');
                  } else {
                      // bezier curve
                      x2 = leg[0] * scalex + x4; // here last x4 is prior ending point
                      y2 = leg[1] * scaley + y4; // here last y4 is prior ending point
                      x3 = leg[2] * scalex + x4; // here last x4 is prior ending point
                      y3 = leg[3] * scaley + y4; // here last y4 is prior ending point
                      x4 = leg[4] * scalex + x4; // here last x4 was prior ending point
                      y4 = leg[5] * scaley + y4; // here last y4 was prior ending point
                      out(f3(x2 * k) + ' ' + f3((pageHeight - y2) * k) + ' ' + f3(x3 * k) + ' ' + f3((pageHeight - y3) * k) + ' ' + f3(x4 * k) + ' ' + f3((pageHeight - y4) * k) + ' c');
                  }
              }

              if (closed) {
                  out(' h');
              }

              // stroking / filling / both the path
              if (style !== null) {
                  out(getStyle(style));
              }
              return this;
          };

          /**
           * Adds a rectangle to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} w Width (in units declared at inception of PDF document)
           * @param {Number} h Height (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name rect
           */
          API.rect = function (x, y, w, h, style) {
              var op = getStyle(style);
              out([f2(x * k), f2((pageHeight - y) * k), f2(w * k), f2(-h * k), 're'].join(' '));

              if (style !== null) {
                  out(getStyle(style));
              }

              return this;
          };

          /**
           * Adds a triangle to PDF
           *
           * @param {Number} x1 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y1 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} x2 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y2 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} x3 Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y3 Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name triangle
           */
          API.triangle = function (x1, y1, x2, y2, x3, y3, style) {
              this.lines([[x2 - x1, y2 - y1], // vector to point 2
              [x3 - x2, y3 - y2], // vector to point 3
              [x1 - x3, y1 - y3] // closing vector back to point 1
              ], x1, y1, // start of path
              [1, 1], style, true);
              return this;
          };

          /**
           * Adds a rectangle with rounded corners to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} w Width (in units declared at inception of PDF document)
           * @param {Number} h Height (in units declared at inception of PDF document)
           * @param {Number} rx Radius along x axis (in units declared at inception of PDF document)
           * @param {Number} rx Radius along y axis (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name roundedRect
           */
          API.roundedRect = function (x, y, w, h, rx, ry, style) {
              var MyArc = 4 / 3 * (Math.SQRT2 - 1);
              this.lines([[w - 2 * rx, 0], [rx * MyArc, 0, rx, ry - ry * MyArc, rx, ry], [0, h - 2 * ry], [0, ry * MyArc, -(rx * MyArc), ry, -rx, ry], [-w + 2 * rx, 0], [-(rx * MyArc), 0, -rx, -(ry * MyArc), -rx, -ry], [0, -h + 2 * ry], [0, -(ry * MyArc), rx * MyArc, -ry, rx, -ry]], x + rx, y, // start of path
              [1, 1], style);
              return this;
          };

          /**
           * Adds an ellipse to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} rx Radius along x axis (in units declared at inception of PDF document)
           * @param {Number} rx Radius along y axis (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name ellipse
           */
          API.ellipse = function (x, y, rx, ry, style) {
              var lx = 4 / 3 * (Math.SQRT2 - 1) * rx,
                  ly = 4 / 3 * (Math.SQRT2 - 1) * ry;

              out([f2((x + rx) * k), f2((pageHeight - y) * k), 'm', f2((x + rx) * k), f2((pageHeight - (y - ly)) * k), f2((x + lx) * k), f2((pageHeight - (y - ry)) * k), f2(x * k), f2((pageHeight - (y - ry)) * k), 'c'].join(' '));
              out([f2((x - lx) * k), f2((pageHeight - (y - ry)) * k), f2((x - rx) * k), f2((pageHeight - (y - ly)) * k), f2((x - rx) * k), f2((pageHeight - y) * k), 'c'].join(' '));
              out([f2((x - rx) * k), f2((pageHeight - (y + ly)) * k), f2((x - lx) * k), f2((pageHeight - (y + ry)) * k), f2(x * k), f2((pageHeight - (y + ry)) * k), 'c'].join(' '));
              out([f2((x + lx) * k), f2((pageHeight - (y + ry)) * k), f2((x + rx) * k), f2((pageHeight - (y + ly)) * k), f2((x + rx) * k), f2((pageHeight - y) * k), 'c'].join(' '));

              if (style !== null) {
                  out(getStyle(style));
              }

              return this;
          };

          /**
           * Adds an circle to PDF
           *
           * @param {Number} x Coordinate (in units declared at inception of PDF document) against left edge of the page
           * @param {Number} y Coordinate (in units declared at inception of PDF document) against upper edge of the page
           * @param {Number} r Radius (in units declared at inception of PDF document)
           * @param {String} style A string specifying the painting style or null.  Valid styles include: 'S' [default] - stroke, 'F' - fill,  and 'DF' (or 'FD') -  fill then stroke. A null value postpones setting the style so that a shape may be composed using multiple method calls. The last drawing method call used to define the shape should not have a null style argument.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name circle
           */
          API.circle = function (x, y, r, style) {
              return this.ellipse(x, y, r, r, style);
          };

          /**
           * Adds a properties to the PDF document
           *
           * @param {Object} A property_name-to-property_value object structure.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setProperties
           */
          API.setProperties = function (properties) {
              // copying only those properties we can render.
              for (var property in documentProperties) {
                  if (documentProperties.hasOwnProperty(property) && properties[property]) {
                      documentProperties[property] = properties[property];
                  }
              }
              return this;
          };

          /**
           * Sets font size for upcoming text elements.
           *
           * @param {Number} size Font size in points.
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFontSize
           */
          API.setFontSize = function (size) {
              activeFontSize = size;
              return this;
          };

          /**
           * Sets text font face, variant for upcoming text elements.
           * See output of jsPDF.getFontList() for possible font names, styles.
           *
           * @param {String} fontName Font name or family. Example: "times"
           * @param {String} fontStyle Font style or variant. Example: "italic"
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFont
           */
          API.setFont = function (fontName, fontStyle) {
              activeFontKey = _getFont(fontName, fontStyle);
              // if font is not found, the above line blows up and we never go further
              return this;
          };

          /**
           * Switches font style or variant for upcoming text elements,
           * while keeping the font face or family same.
           * See output of jsPDF.getFontList() for possible font names, styles.
           *
           * @param {String} style Font style or variant. Example: "italic"
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFontStyle
           */
          API.setFontStyle = API.setFontType = function (style) {
              activeFontKey = _getFont(undefined, style);
              // if font is not found, the above line blows up and we never go further
              return this;
          };

          /**
           * Returns an object - a tree of fontName to fontStyle relationships available to
           * active PDF document.
           *
           * @public
           * @function
           * @returns {Object} Like {'times':['normal', 'italic', ... ], 'arial':['normal', 'bold', ... ], ... }
           * @methodOf jsPDF#
           * @name getFontList
           */
          API.getFontList = function () {
              // TODO: iterate over fonts array or return copy of fontmap instead in case more are ever added.
              var list = {},
                  fontName,
                  fontStyle,
                  tmp;

              for (fontName in fontmap) {
                  if (fontmap.hasOwnProperty(fontName)) {
                      list[fontName] = tmp = [];
                      for (fontStyle in fontmap[fontName]) {
                          if (fontmap[fontName].hasOwnProperty(fontStyle)) {
                              tmp.push(fontStyle);
                          }
                      }
                  }
              }

              return list;
          };

          /**
           * Add a custom font.
           *
           * @param {String} Postscript name of the Font.  Example: "Menlo-Regular"
           * @param {String} Name of font-family from @font-face definition.  Example: "Menlo Regular"
           * @param {String} Font style.  Example: "normal"
           * @function
           * @returns the {fontKey} (same as the internal method)
           * @methodOf jsPDF#
           * @name addFont
           */
          API.addFont = function (postScriptName, fontName, fontStyle) {
              addFont(postScriptName, fontName, fontStyle, 'StandardEncoding');
          };

          /**
           * Sets line width for upcoming lines.
           *
           * @param {Number} width Line width (in units declared at inception of PDF document)
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineWidth
           */
          API.setLineWidth = function (width) {
              out((width * k).toFixed(2) + ' w');
              return this;
          };

          /**
           * Sets the stroke color for upcoming elements.
           *
           * Depending on the number of arguments given, Gray, RGB, or CMYK
           * color space is implied.
           *
           * When only ch1 is given, "Gray" color space is implied and it
           * must be a value in the range from 0.00 (solid black) to to 1.00 (white)
           * if values are communicated as String types, or in range from 0 (black)
           * to 255 (white) if communicated as Number type.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When only ch1,ch2,ch3 are given, "RGB" color space is implied and each
           * value must be in the range from 0.00 (minimum intensity) to to 1.00
           * (max intensity) if values are communicated as String types, or
           * from 0 (min intensity) to to 255 (max intensity) if values are communicated
           * as Number types.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When ch1,ch2,ch3,ch4 are given, "CMYK" color space is implied and each
           * value must be a in the range from 0.00 (0% concentration) to to
           * 1.00 (100% concentration)
           *
           * Because JavaScript treats fixed point numbers badly (rounds to
           * floating point nearest to binary representation) it is highly advised to
           * communicate the fractional numbers as String types, not JavaScript Number type.
           *
           * @param {Number|String} ch1 Color channel value
           * @param {Number|String} ch2 Color channel value
           * @param {Number|String} ch3 Color channel value
           * @param {Number|String} ch4 Color channel value
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setDrawColor
           */
          API.setDrawColor = function (ch1, ch2, ch3, ch4) {
              var color;
              if (ch2 === undefined || ch4 === undefined && ch1 === ch2 === ch3) {
                  // Gray color space.
                  if (typeof ch1 === 'string') {
                      color = ch1 + ' G';
                  } else {
                      color = f2(ch1 / 255) + ' G';
                  }
              } else if (ch4 === undefined) {
                  // RGB
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, 'RG'].join(' ');
                  } else {
                      color = [f2(ch1 / 255), f2(ch2 / 255), f2(ch3 / 255), 'RG'].join(' ');
                  }
              } else {
                  // CMYK
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, ch4, 'K'].join(' ');
                  } else {
                      color = [f2(ch1), f2(ch2), f2(ch3), f2(ch4), 'K'].join(' ');
                  }
              }

              out(color);
              return this;
          };

          /**
           * Sets the fill color for upcoming elements.
           *
           * Depending on the number of arguments given, Gray, RGB, or CMYK
           * color space is implied.
           *
           * When only ch1 is given, "Gray" color space is implied and it
           * must be a value in the range from 0.00 (solid black) to to 1.00 (white)
           * if values are communicated as String types, or in range from 0 (black)
           * to 255 (white) if communicated as Number type.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When only ch1,ch2,ch3 are given, "RGB" color space is implied and each
           * value must be in the range from 0.00 (minimum intensity) to to 1.00
           * (max intensity) if values are communicated as String types, or
           * from 0 (min intensity) to to 255 (max intensity) if values are communicated
           * as Number types.
           * The RGB-like 0-255 range is provided for backward compatibility.
           *
           * When ch1,ch2,ch3,ch4 are given, "CMYK" color space is implied and each
           * value must be a in the range from 0.00 (0% concentration) to to
           * 1.00 (100% concentration)
           *
           * Because JavaScript treats fixed point numbers badly (rounds to
           * floating point nearest to binary representation) it is highly advised to
           * communicate the fractional numbers as String types, not JavaScript Number type.
           *
           * @param {Number|String} ch1 Color channel value
           * @param {Number|String} ch2 Color channel value
           * @param {Number|String} ch3 Color channel value
           * @param {Number|String} ch4 Color channel value
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setFillColor
           */
          API.setFillColor = function (ch1, ch2, ch3, ch4) {
              var color;

              if (ch2 === undefined || ch4 === undefined && ch1 === ch2 === ch3) {
                  // Gray color space.
                  if (typeof ch1 === 'string') {
                      color = ch1 + ' g';
                  } else {
                      color = f2(ch1 / 255) + ' g';
                  }
              } else if (ch4 === undefined || (typeof ch4 === 'undefined' ? 'undefined' : _typeof(ch4)) === 'object') {
                  // RGB
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, 'rg'].join(' ');
                  } else {
                      color = [f2(ch1 / 255), f2(ch2 / 255), f2(ch3 / 255), 'rg'].join(' ');
                  }
                  if (ch4 && ch4.a === 0) {
                      //TODO Implement transparency.
                      //WORKAROUND use white for now
                      color = ['255', '255', '255', 'rg'].join(' ');
                  }
              } else {
                  // CMYK
                  if (typeof ch1 === 'string') {
                      color = [ch1, ch2, ch3, ch4, 'k'].join(' ');
                  } else {
                      color = [f2(ch1), f2(ch2), f2(ch3), f2(ch4), 'k'].join(' ');
                  }
              }

              out(color);
              return this;
          };

          /**
           * Sets the text color for upcoming elements.
           * If only one, first argument is given,
           * treats the value as gray-scale color value.
           *
           * @param {Number} r Red channel color value in range 0-255 or {String} r color value in hexadecimal, example: '#FFFFFF'
           * @param {Number} g Green channel color value in range 0-255
           * @param {Number} b Blue channel color value in range 0-255
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setTextColor
           */
          API.setTextColor = function (r, g, b) {
              if (typeof r === 'string' && /^#[0-9A-Fa-f]{6}$/.test(r)) {
                  var hex = parseInt(r.substr(1), 16);
                  r = hex >> 16 & 255;
                  g = hex >> 8 & 255;
                  b = hex & 255;
              }

              if (r === 0 && g === 0 && b === 0 || typeof g === 'undefined') {
                  textColor = f3(r / 255) + ' g';
              } else {
                  textColor = [f3(r / 255), f3(g / 255), f3(b / 255), 'rg'].join(' ');
              }
              return this;
          };

          /**
           * Is an Object providing a mapping from human-readable to
           * integer flag values designating the varieties of line cap
           * and join styles.
           *
           * @returns {Object}
           * @fieldOf jsPDF#
           * @name CapJoinStyles
           */
          API.CapJoinStyles = {
              0: 0,
              'butt': 0,
              'but': 0,
              'miter': 0,
              1: 1,
              'round': 1,
              'rounded': 1,
              'circle': 1,
              2: 2,
              'projecting': 2,
              'project': 2,
              'square': 2,
              'bevel': 2
          };

          /**
           * Sets the line cap styles
           * See {jsPDF.CapJoinStyles} for variants
           *
           * @param {String|Number} style A string or number identifying the type of line cap
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineCap
           */
          API.setLineCap = function (style) {
              var id = this.CapJoinStyles[style];
              if (id === undefined) {
                  throw new Error("Line cap style of '" + style + "' is not recognized. See or extend .CapJoinStyles property for valid styles");
              }
              lineCapID = id;
              out(id + ' J');

              return this;
          };

          /**
           * Sets the line join styles
           * See {jsPDF.CapJoinStyles} for variants
           *
           * @param {String|Number} style A string or number identifying the type of line join
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name setLineJoin
           */
          API.setLineJoin = function (style) {
              var id = this.CapJoinStyles[style];
              if (id === undefined) {
                  throw new Error("Line join style of '" + style + "' is not recognized. See or extend .CapJoinStyles property for valid styles");
              }
              lineJoinID = id;
              out(id + ' j');

              return this;
          };

          // Output is both an internal (for plugins) and external function
          API.output = _output;

          /**
           * Saves as PDF document. An alias of jsPDF.output('save', 'filename.pdf')
           * @param  {String} filename The filename including extension.
           *
           * @function
           * @returns {jsPDF}
           * @methodOf jsPDF#
           * @name save
           */
          API.save = function (filename) {
              API.output('save', filename);
          };

          // applying plugins (more methods) ON TOP of built-in API.
          // this is intentional as we allow plugins to override
          // built-ins
          for (var plugin in jsPDF.API) {
              if (jsPDF.API.hasOwnProperty(plugin)) {
                  if (plugin === 'events' && jsPDF.API.events.length) {
                      (function (events, newEvents) {

                          // jsPDF.API.events is a JS Array of Arrays
                          // where each Array is a pair of event name, handler
                          // Events were added by plugins to the jsPDF instantiator.
                          // These are always added to the new instance and some ran
                          // during instantiation.
                          var eventname, handler_and_args, i;

                          for (i = newEvents.length - 1; i !== -1; i--) {
                              // subscribe takes 3 args: 'topic', function, runonce_flag
                              // if undefined, runonce is false.
                              // users can attach callback directly,
                              // or they can attach an array with [callback, runonce_flag]
                              // that's what the "apply" magic is for below.
                              eventname = newEvents[i][0];
                              handler_and_args = newEvents[i][1];
                              events.subscribe.apply(events, [eventname].concat(typeof handler_and_args === 'function' ? [handler_and_args] : handler_and_args));
                          }
                      })(events, jsPDF.API.events);
                  } else {
                      API[plugin] = jsPDF.API[plugin];
                  }
              }
          }

          //////////////////////////////////////////////////////
          // continuing initialization of jsPDF Document object
          //////////////////////////////////////////////////////
          // Add the first page automatically
          addFonts();
          activeFontKey = 'F1';
          _addPage(format, orientation);

          events.publish('initialized');
          return API;
      }

      /**
       * jsPDF.API is a STATIC property of jsPDF class.
       * jsPDF.API is an object you can add methods and properties to.
       * The methods / properties you add will show up in new jsPDF objects.
       *
       * One property is prepopulated. It is the 'events' Object. Plugin authors can add topics,
       * callbacks to this object. These will be reassigned to all new instances of jsPDF.
       * Examples:
       * jsPDF.API.events['initialized'] = function(){ 'this' is API object }
       * jsPDF.API.events['addFont'] = function(added_font_object){ 'this' is API object }
       *
       * @static
       * @public
       * @memberOf jsPDF
       * @name API
       *
       * @example
       * jsPDF.API.mymethod = function(){
      *   // 'this' will be ref to internal API object. see jsPDF source
      *   // , so you can refer to built-in methods like so:
      *   //     this.line(....)
      *   //     this.text(....)
      * }
       * var pdfdoc = new jsPDF()
       * pdfdoc.mymethod() // <- !!!!!!
       */
      jsPDF.API = { events: [] };
      jsPDF.version = "1.3.0 2016-09-28T10:52:40.410Z:jameshall";

      if (typeof define === 'function' && define.amd) {
          define('jsPDF', function () {
              return jsPDF;
          });
      } else if (typeof module !== 'undefined' && module.exports) {
          module.exports = jsPDF;
      } else {
          global.jsPDF = jsPDF;
      }
      return jsPDF;
  }(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined);
  /*rollup-keeper-start*/window.tmp = jsPDF;

  /**
   * jsPDF AcroForm Plugin
   * Copyright (c) 2016 Alexander Weidt, https://github.com/BiggA94
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (window.AcroForm = function (jsPDFAPI) {
      'use strict';

      var AcroForm = window.AcroForm;

      AcroForm.scale = function (x) {
          return x * (acroformPlugin.internal.scaleFactor / 1); // 1 = (96 / 72)
      };
      AcroForm.antiScale = function (x) {
          return 1 / acroformPlugin.internal.scaleFactor * x;
      };

      var acroformPlugin = {
          fields: [],
          xForms: [],
          /**
           * acroFormDictionaryRoot contains information about the AcroForm Dictionary
           * 0: The Event-Token, the AcroFormDictionaryCallback has
           * 1: The Object ID of the Root
           */
          acroFormDictionaryRoot: null,
          /**
           * After the PDF gets evaluated, the reference to the root has to be reset,
           * this indicates, whether the root has already been printed out
           */
          printedOut: false,
          internal: null
      };

      jsPDF.API.acroformPlugin = acroformPlugin;

      var annotReferenceCallback = function annotReferenceCallback() {
          for (var i in this.acroformPlugin.acroFormDictionaryRoot.Fields) {
              var formObject = this.acroformPlugin.acroFormDictionaryRoot.Fields[i];
              // add Annot Reference!
              if (formObject.hasAnnotation) {
                  // If theres an Annotation Widget in the Form Object, put the Reference in the /Annot array
                  createAnnotationReference.call(this, formObject);
              }
          }
      };

      var createAcroForm = function createAcroForm() {
          if (this.acroformPlugin.acroFormDictionaryRoot) {
              //return;
              throw new Error("Exception while creating AcroformDictionary");
          }

          // The Object Number of the AcroForm Dictionary
          this.acroformPlugin.acroFormDictionaryRoot = new AcroForm.AcroFormDictionary();

          this.acroformPlugin.internal = this.internal;

          // add Callback for creating the AcroForm Dictionary
          this.acroformPlugin.acroFormDictionaryRoot._eventID = this.internal.events.subscribe('postPutResources', AcroFormDictionaryCallback);

          this.internal.events.subscribe('buildDocument', annotReferenceCallback); //buildDocument

          // Register event, that is triggered when the DocumentCatalog is written, in order to add /AcroForm
          this.internal.events.subscribe('putCatalog', putCatalogCallback);

          // Register event, that creates all Fields
          this.internal.events.subscribe('postPutPages', createFieldCallback);
      };

      /**
       * Create the Reference to the widgetAnnotation, so that it gets referenced in the Annot[] int the+
       * (Requires the Annotation Plugin)
       */
      var createAnnotationReference = function createAnnotationReference(object) {
          var options = {
              type: 'reference',
              object: object
          };
          jsPDF.API.annotationPlugin.annotations[this.internal.getPageInfo(object.page).pageNumber].push(options);
      };

      var putForm = function putForm(formObject) {
          if (this.acroformPlugin.printedOut) {
              this.acroformPlugin.printedOut = false;
              this.acroformPlugin.acroFormDictionaryRoot = null;
          }
          if (!this.acroformPlugin.acroFormDictionaryRoot) {
              createAcroForm.call(this);
          }
          this.acroformPlugin.acroFormDictionaryRoot.Fields.push(formObject);
      };

      // Callbacks

      var putCatalogCallback = function putCatalogCallback() {
          //Put reference to AcroForm to DocumentCatalog
          if (typeof this.acroformPlugin.acroFormDictionaryRoot != 'undefined') {
              // for safety, shouldn't normally be the case
              this.internal.write('/AcroForm ' + this.acroformPlugin.acroFormDictionaryRoot.objId + ' ' + 0 + ' R');
          } else {
              console.log('Root missing...');
          }
      };

      /**
       * Adds /Acroform X 0 R to Document Catalog,
       * and creates the AcroForm Dictionary
       */
      var AcroFormDictionaryCallback = function AcroFormDictionaryCallback() {
          // Remove event
          this.internal.events.unsubscribe(this.acroformPlugin.acroFormDictionaryRoot._eventID);

          delete this.acroformPlugin.acroFormDictionaryRoot._eventID;

          this.acroformPlugin.printedOut = true;
      };

      /**
       * Creates the single Fields and writes them into the Document
       *
       * If fieldArray is set, use the fields that are inside it instead of the fields from the AcroRoot
       * (for the FormXObjects...)
       */
      var createFieldCallback = function createFieldCallback(fieldArray) {
          var standardFields = !fieldArray;

          if (!fieldArray) {
              // in case there is no fieldArray specified, we want to print out the Fields of the AcroForm
              // Print out Root
              this.internal.newObjectDeferredBegin(this.acroformPlugin.acroFormDictionaryRoot.objId);
              this.internal.out(this.acroformPlugin.acroFormDictionaryRoot.getString());
          }

          var fieldArray = fieldArray || this.acroformPlugin.acroFormDictionaryRoot.Kids;

          for (var i in fieldArray) {
              var key = i;
              var form = fieldArray[i];

              var oldRect = form.Rect;

              if (form.Rect) {
                  form.Rect = AcroForm.internal.calculateCoordinates.call(this, form.Rect);
              }

              // Start Writing the Object
              this.internal.newObjectDeferredBegin(form.objId);

              var content = "";
              content += form.objId + " 0 obj\n";

              content += "<<\n" + form.getContent();

              form.Rect = oldRect;

              if (form.hasAppearanceStream && !form.appearanceStreamContent) {
                  // Calculate Appearance
                  var appearance = AcroForm.internal.calculateAppearanceStream.call(this, form);
                  content += "/AP << /N " + appearance + " >>\n";

                  this.acroformPlugin.xForms.push(appearance);
              }

              // Assume AppearanceStreamContent is a Array with N,R,D (at least one of them!)
              if (form.appearanceStreamContent) {
                  content += "/AP << ";
                  // Iterate over N,R and D
                  for (var k in form.appearanceStreamContent) {
                      var value = form.appearanceStreamContent[k];
                      content += "/" + k + " ";
                      content += "<< ";
                      if (Object.keys(value).length >= 1 || Array.isArray(value)) {
                          // appearanceStream is an Array or Object!
                          for (var i in value) {
                              var obj = value[i];
                              if (typeof obj === 'function') {
                                  // if Function is referenced, call it in order to get the FormXObject
                                  obj = obj.call(this, form);
                              }
                              content += "/" + i + " " + obj + " ";

                              // In case the XForm is already used, e.g. OffState of CheckBoxes, don't add it
                              if (!(this.acroformPlugin.xForms.indexOf(obj) >= 0)) this.acroformPlugin.xForms.push(obj);
                          }
                      } else {
                          var obj = value;
                          if (typeof obj === 'function') {
                              // if Function is referenced, call it in order to get the FormXObject
                              obj = obj.call(this, form);
                          }
                          content += "/" + i + " " + obj + " \n";
                          if (!(this.acroformPlugin.xForms.indexOf(obj) >= 0)) this.acroformPlugin.xForms.push(obj);
                      }
                      content += " >>\n";
                  }

                  // appearance stream is a normal Object..
                  content += ">>\n";
              }

              content += ">>\nendobj\n";

              this.internal.out(content);
          }
          if (standardFields) {
              createXFormObjectCallback.call(this, this.acroformPlugin.xForms);
          }
      };

      var createXFormObjectCallback = function createXFormObjectCallback(fieldArray) {
          for (var i in fieldArray) {
              var key = i;
              var form = fieldArray[i];
              // Start Writing the Object
              this.internal.newObjectDeferredBegin(form && form.objId);

              var content = "";
              content += form ? form.getString() : '';
              this.internal.out(content);

              delete fieldArray[key];
          }
      };

      // Public:

      jsPDFAPI.addField = function (fieldObject) {
          //var opt = parseOptions(fieldObject);
          if (fieldObject instanceof AcroForm.TextField) {
              addTextField.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.ChoiceField) {
              addChoiceField.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.Button) {
              addButton.call(this, fieldObject);
          } else if (fieldObject instanceof AcroForm.ChildClass) {
              putForm.call(this, fieldObject);
          } else if (fieldObject) {
              // try to put..
              putForm.call(this, fieldObject);
          }
          fieldObject.page = this.acroformPlugin.internal.getCurrentPageInfo().pageNumber;
          return this;
      };

      // ############### sort in:

      /**
       * Button
       * FT = Btn
       */
      var addButton = function addButton(options) {
          var options = options || new AcroForm.Field();

          options.FT = '/Btn';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */
          var flags = options.Ff || 0;

          // 17, Pushbutton
          if (options.pushbutton) {
              // Options.pushbutton should be 1 or 0
              flags = AcroForm.internal.setBitPosition(flags, 17);
              delete options.pushbutton;
          }

          //16, Radio
          if (options.radio) {
              //flags = options.Ff | options.radio << 15;
              flags = AcroForm.internal.setBitPosition(flags, 16);
              delete options.radio;
          }

          // 15, NoToggleToOff (Radio buttons only
          if (options.noToggleToOff) {
              //flags = options.Ff | options.noToggleToOff << 14;
              flags = AcroForm.internal.setBitPosition(flags, 15);
              //delete options.noToggleToOff;
          }

          // In case, there is no Flag set, it is a check-box
          options.Ff = flags;

          putForm.call(this, options);
      };

      var addTextField = function addTextField(options) {
          var options = options || new AcroForm.Field();

          options.FT = '/Tx';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */

          var flags = options.Ff || 0;

          // 13, multiline
          if (options.multiline) {
              // Set Flag
              flags = flags | 1 << 12;
              // Remove multiline from FieldObject
              //delete options.multiline;
          }

          // 14, Password
          if (options.password) {
              flags = flags | 1 << 13;
              //delete options.password;
          }

          // 21, FileSelect, PDF 1.4...
          if (options.fileSelect) {
              flags = flags | 1 << 20;
              //delete options.fileSelect;
          }

          // 23, DoNotSpellCheck, PDF 1.4...
          if (options.doNotSpellCheck) {
              flags = flags | 1 << 22;
              //delete options.doNotSpellCheck;
          }

          // 24, DoNotScroll, PDF 1.4...
          if (options.doNotScroll) {
              flags = flags | 1 << 23;
              //delete options.doNotScroll;
          }

          options.Ff = options.Ff || flags;

          // Add field
          putForm.call(this, options);
      };

      var addChoiceField = function addChoiceField(opt) {
          var options = opt || new AcroForm.Field();

          options.FT = '/Ch';

          /**
           * Calculating the Ff entry:
           *
           * The Ff entry contains flags, that have to be set bitwise
           * In the Following the number in the Comment is the BitPosition
           */

          var flags = options.Ff || 0;

          // 18, Combo (If not set, the choiceField is a listBox!!)
          if (options.combo) {
              // Set Flag
              flags = AcroForm.internal.setBitPosition(flags, 18);
              // Remove combo from FieldObject
              delete options.combo;
          }

          // 19, Edit
          if (options.edit) {
              flags = AcroForm.internal.setBitPosition(flags, 19);
              delete options.edit;
          }

          // 20, Sort
          if (options.sort) {
              flags = AcroForm.internal.setBitPosition(flags, 20);
              delete options.sort;
          }

          // 22, MultiSelect (PDF 1.4)
          if (options.multiSelect && this.internal.getPDFVersion() >= 1.4) {
              flags = AcroForm.internal.setBitPosition(flags, 22);
              delete options.multiSelect;
          }

          // 23, DoNotSpellCheck (PDF 1.4)
          if (options.doNotSpellCheck && this.internal.getPDFVersion() >= 1.4) {
              flags = AcroForm.internal.setBitPosition(flags, 23);
              delete options.doNotSpellCheck;
          }

          options.Ff = flags;

          //options.hasAnnotation = true;

          // Add field
          putForm.call(this, options);
      };
  })(jsPDF.API);

  var AcroForm = window.AcroForm;

  AcroForm.internal = {};

  AcroForm.createFormXObject = function (formObject) {
      var xobj = new AcroForm.FormXObject();
      var height = AcroForm.Appearance.internal.getHeight(formObject) || 0;
      var width = AcroForm.Appearance.internal.getWidth(formObject) || 0;
      xobj.BBox = [0, 0, width, height];
      return xobj;
  };

  // Contains Methods for creating standard appearances
  AcroForm.Appearance = {
      CheckBox: {
          createAppearanceStream: function createAppearanceStream() {
              var appearance = {
                  N: {
                      On: AcroForm.Appearance.CheckBox.YesNormal
                  },
                  D: {
                      On: AcroForm.Appearance.CheckBox.YesPushDown,
                      Off: AcroForm.Appearance.CheckBox.OffPushDown
                  }
              };

              return appearance;
          },
          /**
           * If any other icons are needed, the number between the brackets can be changed
           * @returns {string}
           */
          createMK: function createMK() {
              // 3-> Hook
              return "<< /CA (3)>>";
          },
          /**
           * Returns the standard On Appearance for a CheckBox
           * @returns {AcroForm.FormXObject}
           */
          YesPushDown: function YesPushDown(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              // F13 is ZapfDingbats (Symbolic)
              formObject.Q = 1; // set text-alignment as centered
              var calcRes = AcroForm.internal.calculateX(formObject, "3", "ZapfDingbats", 50);
              stream += "0.749023 g\n\
             0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
             f\n\
             BMC\n\
             q\n\
             0 0 1 rg\n\
             /F13 " + calcRes.fontSize + " Tf 0 g\n\
             BT\n";
              stream += calcRes.text;
              stream += "ET\n\
             Q\n\
             EMC\n";
              xobj.stream = stream;
              return xobj;
          },

          YesNormal: function YesNormal(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              formObject.Q = 1; // set text-alignment as centered
              var calcRes = AcroForm.internal.calculateX(formObject, "3", "ZapfDingbats", AcroForm.Appearance.internal.getHeight(formObject) * 0.9);
              stream += "1 g\n\
0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
f\n\
q\n\
0 0 1 rg\n\
0 0 " + (AcroForm.Appearance.internal.getWidth(formObject) - 1) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 1) + " re\n\
W\n\
n\n\
0 g\n\
BT\n\
/F13 " + calcRes.fontSize + " Tf 0 g\n";
              stream += calcRes.text;
              stream += "ET\n\
             Q\n";
              xobj.stream = stream;
              return xobj;
          },

          /**
           * Returns the standard Off Appearance for a CheckBox
           * @returns {AcroForm.FormXObject}
           */
          OffPushDown: function OffPushDown(formObject) {
              var xobj = AcroForm.createFormXObject(formObject);
              var stream = "";
              stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n";
              xobj.stream = stream;
              return xobj;
          }
      },

      RadioButton: {
          Circle: {
              createAppearanceStream: function createAppearanceStream(name) {
                  var appearanceStreamContent = {
                      D: {
                          'Off': AcroForm.Appearance.RadioButton.Circle.OffPushDown
                      },
                      N: {}
                  };
                  appearanceStreamContent.N[name] = AcroForm.Appearance.RadioButton.Circle.YesNormal;
                  appearanceStreamContent.D[name] = AcroForm.Appearance.RadioButton.Circle.YesPushDown;
                  return appearanceStreamContent;
              },
              createMK: function createMK() {
                  return "<< /CA (l)>>";
              },

              YesNormal: function YesNormal(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  // Make the Radius of the Circle relative to min(height, width) of formObject
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  /*
                   The Following is a Circle created with Bezier-Curves.
                   */
                  stream += "q\n\
1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius + " 0 m\n\
" + DotRadius + " " + DotRadius * c + " " + DotRadius * c + " " + DotRadius + " 0 " + DotRadius + " c\n\
-" + DotRadius * c + " " + DotRadius + " -" + DotRadius + " " + DotRadius * c + " -" + DotRadius + " 0 c\n\
-" + DotRadius + " -" + DotRadius * c + " -" + DotRadius * c + " -" + DotRadius + " 0 -" + DotRadius + " c\n\
" + DotRadius * c + " -" + DotRadius + " " + DotRadius + " -" + DotRadius * c + " " + DotRadius + " 0 c\n\
f\n\
Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              YesPushDown: function YesPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  stream += "0.749023 g\n\
            q\n\
           1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius * 2 + " 0 m\n\
" + DotRadius * 2 + " " + DotRadius * 2 * c + " " + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 " + DotRadius * 2 + " c\n\
-" + DotRadius * 2 * c + " " + DotRadius * 2 + " -" + DotRadius * 2 + " " + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 c\n\
-" + DotRadius * 2 + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 -" + DotRadius * 2 + " c\n\
" + DotRadius * 2 * c + " -" + DotRadius * 2 + " " + DotRadius * 2 + " -" + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 c\n\
            f\n\
            Q\n\
            0 g\n\
            q\n\
            1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius + " 0 m\n\
" + DotRadius + " " + DotRadius * c + " " + DotRadius * c + " " + DotRadius + " 0 " + DotRadius + " c\n\
-" + DotRadius * c + " " + DotRadius + " -" + DotRadius + " " + DotRadius * c + " -" + DotRadius + " 0 c\n\
-" + DotRadius + " -" + DotRadius * c + " -" + DotRadius * c + " -" + DotRadius + " 0 -" + DotRadius + " c\n\
" + DotRadius * c + " -" + DotRadius + " " + DotRadius + " -" + DotRadius * c + " " + DotRadius + " 0 c\n\
            f\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              OffPushDown: function OffPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var DotRadius = AcroForm.Appearance.internal.getWidth(formObject) <= AcroForm.Appearance.internal.getHeight(formObject) ? AcroForm.Appearance.internal.getWidth(formObject) / 4 : AcroForm.Appearance.internal.getHeight(formObject) / 4;
                  // The Borderpadding...
                  DotRadius *= 0.9;
                  var c = AcroForm.Appearance.internal.Bezier_C;
                  stream += "0.749023 g\n\
            q\n\
 1 0 0 1 " + AcroForm.Appearance.internal.getWidth(formObject) / 2 + " " + AcroForm.Appearance.internal.getHeight(formObject) / 2 + " cm\n\
" + DotRadius * 2 + " 0 m\n\
" + DotRadius * 2 + " " + DotRadius * 2 * c + " " + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 " + DotRadius * 2 + " c\n\
-" + DotRadius * 2 * c + " " + DotRadius * 2 + " -" + DotRadius * 2 + " " + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 c\n\
-" + DotRadius * 2 + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 * c + " -" + DotRadius * 2 + " 0 -" + DotRadius * 2 + " c\n\
" + DotRadius * 2 * c + " -" + DotRadius * 2 + " " + DotRadius * 2 + " -" + DotRadius * 2 * c + " " + DotRadius * 2 + " 0 c\n\
            f\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              }
          },

          Cross: {
              /**
               * Creates the Actual AppearanceDictionary-References
               * @param name
               * @returns
               */
              createAppearanceStream: function createAppearanceStream(name) {
                  var appearanceStreamContent = {
                      D: {
                          'Off': AcroForm.Appearance.RadioButton.Cross.OffPushDown
                      },
                      N: {}
                  };
                  appearanceStreamContent.N[name] = AcroForm.Appearance.RadioButton.Cross.YesNormal;
                  appearanceStreamContent.D[name] = AcroForm.Appearance.RadioButton.Cross.YesPushDown;
                  return appearanceStreamContent;
              },
              createMK: function createMK() {
                  return "<< /CA (8)>>";
              },

              YesNormal: function YesNormal(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  var cross = AcroForm.Appearance.internal.calculateCross(formObject);
                  stream += "q\n\
            1 1 " + (AcroForm.Appearance.internal.getWidth(formObject) - 2) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 2) + " re\n\
            W\n\
            n\n\
            " + cross.x1.x + " " + cross.x1.y + " m\n\
            " + cross.x2.x + " " + cross.x2.y + " l\n\
            " + cross.x4.x + " " + cross.x4.y + " m\n\
            " + cross.x3.x + " " + cross.x3.y + " l\n\
            s\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              YesPushDown: function YesPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var cross = AcroForm.Appearance.internal.calculateCross(formObject);
                  var stream = "";
                  stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n\
            q\n\
            1 1 " + (AcroForm.Appearance.internal.getWidth(formObject) - 2) + " " + (AcroForm.Appearance.internal.getHeight(formObject) - 2) + " re\n\
            W\n\
            n\n\
            " + cross.x1.x + " " + cross.x1.y + " m\n\
            " + cross.x2.x + " " + cross.x2.y + " l\n\
            " + cross.x4.x + " " + cross.x4.y + " m\n\
            " + cross.x3.x + " " + cross.x3.y + " l\n\
            s\n\
            Q\n";
                  xobj.stream = stream;
                  return xobj;
              },
              OffPushDown: function OffPushDown(formObject) {
                  var xobj = AcroForm.createFormXObject(formObject);
                  var stream = "";
                  stream += "0.749023 g\n\
            0 0 " + AcroForm.Appearance.internal.getWidth(formObject) + " " + AcroForm.Appearance.internal.getHeight(formObject) + " re\n\
            f\n";
                  xobj.stream = stream;
                  return xobj;
              }
          }
      },

      /**
       * Returns the standard Appearance
       * @returns {AcroForm.FormXObject}
       */
      createDefaultAppearanceStream: function createDefaultAppearanceStream(formObject) {
          var stream = "";
          // Set Helvetica to Standard Font (size: auto)
          // Color: Black
          stream += "/Helv 0 Tf 0 g";
          return stream;
      }
  };

  AcroForm.Appearance.internal = {
      Bezier_C: 0.551915024494,

      calculateCross: function calculateCross(formObject) {
          var min = function min(x, y) {
              return x > y ? y : x;
          };

          var width = AcroForm.Appearance.internal.getWidth(formObject);
          var height = AcroForm.Appearance.internal.getHeight(formObject);
          var a = min(width, height);
          var crossSize = a;
          var borderPadding = 2; // The Padding in px


          var cross = {
              x1: { // upperLeft
                  x: (width - a) / 2,
                  y: (height - a) / 2 + a },
              x2: { // lowerRight
                  x: (width - a) / 2 + a,
                  y: (height - a) / 2 //borderPadding
              },
              x3: { // lowerLeft
                  x: (width - a) / 2,
                  y: (height - a) / 2 //borderPadding
              },
              x4: { // upperRight
                  x: (width - a) / 2 + a,
                  y: (height - a) / 2 + a }
          };

          return cross;
      }
  };
  AcroForm.Appearance.internal.getWidth = function (formObject) {
      return formObject.Rect[2]; //(formObject.Rect[2] - formObject.Rect[0]) || 0;
  };
  AcroForm.Appearance.internal.getHeight = function (formObject) {
      return formObject.Rect[3]; //(formObject.Rect[1] - formObject.Rect[3]) || 0;
  };

  // ##########################

  //### For inheritance:
  AcroForm.internal.inherit = function (child, parent) {
      var ObjectCreate = Object.create || function (o) {
          var F = function F() {};
          F.prototype = o;
          return new F();
      };
      child.prototype = Object.create(parent.prototype);
      child.prototype.constructor = child;
  };

  // ### Handy Functions:

  AcroForm.internal.arrayToPdfArray = function (array) {
      if (Array.isArray(array)) {
          var content = ' [';
          for (var i in array) {
              var element = array[i].toString();
              content += element;
              content += i < array.length - 1 ? ' ' : '';
          }
          content += ']';

          return content;
      }
  };

  AcroForm.internal.toPdfString = function (string) {
      string = string || "";

      // put Bracket at the Beginning of the String
      if (string.indexOf('(') !== 0) {
          string = '(' + string;
      }

      if (string.substring(string.length - 1) != ')') {
          string += '(';
      }
      return string;
  };

  // ##########################
  //          Classes
  // ##########################


  AcroForm.PDFObject = function () {
      // The Object ID in the PDF Object Model
      // todo
      var _objId;
      Object.defineProperty(this, 'objId', {
          get: function get() {
              if (!_objId) {
                  if (this.internal) {
                      _objId = this.internal.newObjectDeferred();
                  } else if (jsPDF.API.acroformPlugin.internal) {
                      // todo - find better option, that doesn't rely on a Global Static var
                      _objId = jsPDF.API.acroformPlugin.internal.newObjectDeferred();
                  }
              }
              if (!_objId) {
                  console.log("Couldn't create Object ID");
              }
              return _objId;
          },
          configurable: false
      });
  };

  AcroForm.PDFObject.prototype.toString = function () {
      return this.objId + " 0 R";
  };

  AcroForm.PDFObject.prototype.getString = function () {
      var res = this.objId + " 0 obj\n<<";
      var content = this.getContent();

      res += content + ">>\n";
      if (this.stream) {
          res += "stream\n";
          res += this.stream;
          res += "endstream\n";
      }
      res += "endobj\n";
      return res;
  };

  AcroForm.PDFObject.prototype.getContent = function () {
      /**
       * Prints out all enumerable Variables from the Object
       * @param fieldObject
       * @returns {string}
       */
      var createContentFromFieldObject = function createContentFromFieldObject(fieldObject) {
          var content = '';

          var keys = Object.keys(fieldObject).filter(function (key) {
              return key != 'content' && key != 'appearanceStreamContent' && key.substring(0, 1) != "_";
          });

          for (var i in keys) {
              var key = keys[i];
              var value = fieldObject[key];

              /*if (key == 'Rect' && value) {
               value = AcroForm.internal.calculateCoordinates.call(jsPDF.API.acroformPlugin.internal, value);
               }*/

              if (value) {
                  if (Array.isArray(value)) {
                      content += '/' + key + ' ' + AcroForm.internal.arrayToPdfArray(value) + "\n";
                  } else if (value instanceof AcroForm.PDFObject) {
                      // In case it is a reference to another PDFObject, take the referennce number
                      content += '/' + key + ' ' + value.objId + " 0 R" + "\n";
                  } else {
                      content += '/' + key + ' ' + value + '\n';
                  }
              }
          }
          return content;
      };

      var object = "";

      object += createContentFromFieldObject(this);
      return object;
  };

  AcroForm.FormXObject = function () {
      AcroForm.PDFObject.call(this);
      this.Type = "/XObject";
      this.Subtype = "/Form";
      this.FormType = 1;
      this.BBox;
      this.Matrix;
      this.Resources = "2 0 R";
      this.PieceInfo;
      var _stream;
      Object.defineProperty(this, 'Length', {
          enumerable: true,
          get: function get() {
              return _stream !== undefined ? _stream.length : 0;
          }
      });
      Object.defineProperty(this, 'stream', {
          enumerable: false,
          set: function set(val) {
              _stream = val;
          },
          get: function get() {
              if (_stream) {
                  return _stream;
              } else {
                  return null;
              }
          }
      });
  };

  AcroForm.internal.inherit(AcroForm.FormXObject, AcroForm.PDFObject);

  AcroForm.AcroFormDictionary = function () {
      AcroForm.PDFObject.call(this);
      var _Kids = [];
      Object.defineProperty(this, 'Kids', {
          enumerable: false,
          configurable: true,
          get: function get() {
              if (_Kids.length > 0) {
                  return _Kids;
              } else {
                  return;
              }
          }
      });
      Object.defineProperty(this, 'Fields', {
          enumerable: true,
          configurable: true,
          get: function get() {
              return _Kids;
          }
      });
      // Default Appearance
      this.DA;
  };

  AcroForm.internal.inherit(AcroForm.AcroFormDictionary, AcroForm.PDFObject);

  // ##### The Objects, the User can Create:


  // The Field Object contains the Variables, that every Field needs
  // Rectangle for Appearance: lower_left_X, lower_left_Y, width, height
  AcroForm.Field = function () {
      'use strict';

      AcroForm.PDFObject.call(this);

      var _Rect;
      Object.defineProperty(this, 'Rect', {
          enumerable: true,
          configurable: false,
          get: function get() {
              if (!_Rect) {
                  return;
              }
              var tmp = _Rect;
              //var calculatedRes = AcroForm.internal.calculateCoordinates(_Rect); // do later!
              return tmp;
          },
          set: function set(val) {
              _Rect = val;
          }
      });

      var _FT = "";
      Object.defineProperty(this, 'FT', {
          enumerable: true,
          set: function set(val) {
              _FT = val;
          },
          get: function get() {
              return _FT;
          }
      });
      /**
       * The Partial name of the Field Object.
       * It has to be unique.
       */
      var _T;

      Object.defineProperty(this, 'T', {
          enumerable: true,
          configurable: false,
          set: function set(val) {
              _T = val;
          },
          get: function get() {
              if (!_T || _T.length < 1) {
                  if (this instanceof AcroForm.ChildClass) {
                      // In case of a Child from a Radio´Group, you don't need a FieldName!!!
                      return;
                  }
                  return "(FieldObject" + AcroForm.Field.FieldNum++ + ")";
              }
              if (_T.substring(0, 1) == "(" && _T.substring(_T.length - 1)) {
                  return _T;
              }
              return "(" + _T + ")";
          }
      });

      var _DA;
      // Defines the default appearance (Needed for variable Text)
      Object.defineProperty(this, 'DA', {
          enumerable: true,
          get: function get() {
              if (!_DA) {
                  return;
              }
              return '(' + _DA + ')';
          },
          set: function set(val) {
              _DA = val;
          }
      });

      var _DV;
      // Defines the default value
      Object.defineProperty(this, 'DV', {
          enumerable: true,
          configurable: true,
          get: function get() {
              if (!_DV) {
                  return;
              }
              return _DV;
          },
          set: function set(val) {
              _DV = val;
          }
      });

      //this.Type = "/Annot";
      //this.Subtype = "/Widget";
      Object.defineProperty(this, 'Type', {
          enumerable: true,
          get: function get() {
              return this.hasAnnotation ? "/Annot" : null;
          }
      });

      Object.defineProperty(this, 'Subtype', {
          enumerable: true,
          get: function get() {
              return this.hasAnnotation ? "/Widget" : null;
          }
      });

      /**
       *
       * @type {Array}
       */
      this.BG;

      Object.defineProperty(this, 'hasAnnotation', {
          enumerable: false,
          get: function get() {
              if (this.Rect || this.BC || this.BG) {
                  return true;
              }
              return false;
          }
      });

      Object.defineProperty(this, 'hasAppearanceStream', {
          enumerable: false,
          configurable: true,
          writable: true
      });

      Object.defineProperty(this, 'page', {
          enumerable: false,
          configurable: true,
          writable: true
      });
  };
  AcroForm.Field.FieldNum = 0;

  AcroForm.internal.inherit(AcroForm.Field, AcroForm.PDFObject);

  AcroForm.ChoiceField = function () {
      AcroForm.Field.call(this);
      // Field Type = Choice Field
      this.FT = "/Ch";
      // options
      this.Opt = [];
      this.V = '()';
      // Top Index
      this.TI = 0;
      /**
       * Defines, whether the
       * @type {boolean}
       */
      this.combo = false;
      /**
       * Defines, whether the Choice Field is an Edit Field.
       * An Edit Field is automatically an Combo Field.
       */
      Object.defineProperty(this, 'edit', {
          enumerable: true,
          set: function set(val) {
              if (val == true) {
                  this._edit = true;
                  // ComboBox has to be true
                  this.combo = true;
              } else {
                  this._edit = false;
              }
          },
          get: function get() {
              if (!this._edit) {
                  return false;
              }
              return this._edit;
          },
          configurable: false
      });
      this.hasAppearanceStream = true;
      Object.defineProperty(this, 'V', {
          get: function get() {
              AcroForm.internal.toPdfString();
          }
      });
  };
  AcroForm.internal.inherit(AcroForm.ChoiceField, AcroForm.Field);
  window["ChoiceField"] = AcroForm.ChoiceField;

  AcroForm.ListBox = function () {
      AcroForm.ChoiceField.call(this);
      //var combo = true;
  };
  AcroForm.internal.inherit(AcroForm.ListBox, AcroForm.ChoiceField);
  window["ListBox"] = AcroForm.ListBox;

  AcroForm.ComboBox = function () {
      AcroForm.ListBox.call(this);
      this.combo = true;
  };
  AcroForm.internal.inherit(AcroForm.ComboBox, AcroForm.ListBox);
  window["ComboBox"] = AcroForm.ComboBox;

  AcroForm.EditBox = function () {
      AcroForm.ComboBox.call(this);
      this.edit = true;
  };
  AcroForm.internal.inherit(AcroForm.EditBox, AcroForm.ComboBox);
  window["EditBox"] = AcroForm.EditBox;

  AcroForm.Button = function () {
      AcroForm.Field.call(this);
      this.FT = "/Btn";
      //this.hasAnnotation = true;
  };
  AcroForm.internal.inherit(AcroForm.Button, AcroForm.Field);
  window["Button"] = AcroForm.Button;

  AcroForm.PushButton = function () {
      AcroForm.Button.call(this);
      this.pushbutton = true;
  };
  AcroForm.internal.inherit(AcroForm.PushButton, AcroForm.Button);
  window["PushButton"] = AcroForm.PushButton;

  AcroForm.RadioButton = function () {
      AcroForm.Button.call(this);
      this.radio = true;
      var _Kids = [];
      Object.defineProperty(this, 'Kids', {
          enumerable: true,
          get: function get() {
              if (_Kids.length > 0) {
                  return _Kids;
              }
          }
      });

      Object.defineProperty(this, '__Kids', {
          get: function get() {
              return _Kids;
          }
      });

      var _noToggleToOff;

      Object.defineProperty(this, 'noToggleToOff', {
          enumerable: false,
          get: function get() {
              return _noToggleToOff;
          },
          set: function set(val) {
              _noToggleToOff = val;
          }
      });

      //this.hasAnnotation = false;
  };
  AcroForm.internal.inherit(AcroForm.RadioButton, AcroForm.Button);
  window["RadioButton"] = AcroForm.RadioButton;

  /*
   * The Child classs of a RadioButton (the radioGroup)
   * -> The single Buttons
   */
  AcroForm.ChildClass = function (parent, name) {
      AcroForm.Field.call(this);
      this.Parent = parent;

      // todo: set AppearanceType as variable that can be set from the outside...
      this._AppearanceType = AcroForm.Appearance.RadioButton.Circle; // The Default appearanceType is the Circle
      this.appearanceStreamContent = this._AppearanceType.createAppearanceStream(name);

      // Set Print in the Annot Flag
      this.F = AcroForm.internal.setBitPosition(this.F, 3, 1);

      // Set AppearanceCharacteristicsDictionary with default appearance if field is not interacting with user
      this.MK = this._AppearanceType.createMK(); // (8) -> Cross, (1)-> Circle, ()-> nothing

      // Default Appearance is Off
      this.AS = "/Off"; // + name;

      this._Name = name;
  };
  AcroForm.internal.inherit(AcroForm.ChildClass, AcroForm.Field);

  AcroForm.RadioButton.prototype.setAppearance = function (appearance) {
      if (!('createAppearanceStream' in appearance && 'createMK' in appearance)) {
          console.log("Couldn't assign Appearance to RadioButton. Appearance was Invalid!");
          return;
      }
      for (var i in this.__Kids) {
          var child = this.__Kids[i];

          child.appearanceStreamContent = appearance.createAppearanceStream(child._Name);
          child.MK = appearance.createMK();
      }
  };

  AcroForm.RadioButton.prototype.createOption = function (name) {
      var parent = this;
      var kidCount = this.__Kids.length;

      // Create new Child for RadioGroup
      var child = new AcroForm.ChildClass(parent, name);
      // Add to Parent
      this.__Kids.push(child);

      jsPDF.API.addField(child);

      return child;
  };

  AcroForm.CheckBox = function () {
      Button.call(this);
      this.appearanceStreamContent = AcroForm.Appearance.CheckBox.createAppearanceStream();
      this.MK = AcroForm.Appearance.CheckBox.createMK();
      this.AS = "/On";
      this.V = "/On";
  };
  AcroForm.internal.inherit(AcroForm.CheckBox, AcroForm.Button);
  window["CheckBox"] = AcroForm.CheckBox;

  AcroForm.TextField = function () {
      AcroForm.Field.call(this);
      this.DA = AcroForm.Appearance.createDefaultAppearanceStream();
      this.F = 4;
      var _V;
      Object.defineProperty(this, 'V', {
          get: function get() {
              if (_V) {
                  return "(" + _V + ")";
              } else {
                  return _V;
              }
          },
          enumerable: true,
          set: function set(val) {
              _V = val;
          }
      });

      var _DV;
      Object.defineProperty(this, 'DV', {
          get: function get() {
              if (_DV) {
                  return "(" + _DV + ")";
              } else {
                  return _DV;
              }
          },
          enumerable: true,
          set: function set(val) {
              _DV = val;
          }
      });

      var _multiline = false;
      Object.defineProperty(this, 'multiline', {
          enumerable: false,
          get: function get() {
              return _multiline;
          },
          set: function set(val) {
              _multiline = val;
          }
      });

      //this.multiline = false;
      //this.password = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.fileSelect = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.doNotSpellCheck = false;
      /**
       * For PDF 1.4
       * @type {boolean}
       */
      //this.doNotScroll = false;

      var _MaxLen = false;
      Object.defineProperty(this, 'MaxLen', {
          enumerable: true,
          get: function get() {
              return _MaxLen;
          },
          set: function set(val) {
              _MaxLen = val;
          }
      });

      Object.defineProperty(this, 'hasAppearanceStream', {
          enumerable: false,
          get: function get() {
              return this.V || this.DV;
          }
      });
  };
  AcroForm.internal.inherit(AcroForm.TextField, AcroForm.Field);
  window["TextField"] = AcroForm.TextField;

  AcroForm.PasswordField = function () {
      TextField.call(this);
      Object.defineProperty(this, 'password', {
          value: true,
          enumerable: false,
          configurable: false,
          writable: false
      });
  };
  AcroForm.internal.inherit(AcroForm.PasswordField, AcroForm.TextField);
  window["PasswordField"] = AcroForm.PasswordField;

  // ############ internal functions

  /*
   * small workaround for calculating the TextMetric approximately
   * @param text
   * @param fontsize
   * @returns {TextMetrics} (Has Height and Width)
   */
  AcroForm.internal.calculateFontSpace = function (text, fontsize, fonttype) {
      var fonttype = fonttype || "helvetica";
      //re-use canvas object for speed improvements
      var canvas = AcroForm.internal.calculateFontSpace.canvas || (AcroForm.internal.calculateFontSpace.canvas = document.createElement('canvas'));

      var context = canvas.getContext('2d');
      context.save();
      var newFont = fontsize + " " + fonttype;
      context.font = newFont;
      var res = context.measureText(text);
      context.fontcolor = 'black';
      // Calculate height:
      var context = canvas.getContext('2d');
      res.height = context.measureText("3").width * 1.5; // 3 because in ZapfDingbats its a Hook and a 3 in normal fonts
      context.restore();

      var width = res.width;

      return res;
  };

  AcroForm.internal.calculateX = function (formObject, text, font, maxFontSize) {
      var maxFontSize = maxFontSize || 12;
      var font = font || "helvetica";
      var returnValue = {
          text: "",
          fontSize: ""
      };
      // Remove Brackets
      text = text.substr(0, 1) == '(' ? text.substr(1) : text;
      text = text.substr(text.length - 1) == ')' ? text.substr(0, text.length - 1) : text;
      // split into array of words
      var textSplit = text.split(' ');

      /**
       * the color could be ((alpha)||(r,g,b)||(c,m,y,k))
       * @type {string}
       */
      var color = "0 g\n";
      var fontSize = maxFontSize; // The Starting fontSize (The Maximum)
      var lineSpacing = 2;
      var borderPadding = 2;

      var height = AcroForm.Appearance.internal.getHeight(formObject) || 0;
      height = height < 0 ? -height : height;
      var width = AcroForm.Appearance.internal.getWidth(formObject) || 0;
      width = width < 0 ? -width : width;

      var isSmallerThanWidth = function isSmallerThanWidth(i, lastLine, fontSize) {
          if (i + 1 < textSplit.length) {
              var tmp = lastLine + " " + textSplit[i + 1];
              var TextWidth = AcroForm.internal.calculateFontSpace(tmp, fontSize + "px", font).width;
              var FieldWidth = width - 2 * borderPadding;
              return TextWidth <= FieldWidth;
          } else {
              return false;
          }
      };

      fontSize++;
      FontSize: while (true) {
          var text = "";
          fontSize--;
          var textHeight = AcroForm.internal.calculateFontSpace("3", fontSize + "px", font).height;
          var startY = formObject.multiline ? height - fontSize : (height - textHeight) / 2;
          startY += lineSpacing;
          var startX = -borderPadding;

          var lastX = startX,
              lastY = startY;
          var firstWordInLine = 0,
              lastWordInLine = 0;
          var lastLength = 0;

          var y = 0;
          if (fontSize == 0) {
              // In case, the Text doesn't fit at all
              fontSize = 12;
              text = "(...) Tj\n";
              text += "% Width of Text: " + AcroForm.internal.calculateFontSpace(text, "1px").width + ", FieldWidth:" + width + "\n";
              break;
          }

          lastLength = AcroForm.internal.calculateFontSpace(textSplit[0] + " ", fontSize + "px", font).width;

          var lastLine = "";
          var lineCount = 0;
          Line: for (var i in textSplit) {
              lastLine += textSplit[i] + " ";
              // Remove last blank
              lastLine = lastLine.substr(lastLine.length - 1) == " " ? lastLine.substr(0, lastLine.length - 1) : lastLine;
              var key = parseInt(i);
              lastLength = AcroForm.internal.calculateFontSpace(lastLine + " ", fontSize + "px", font).width;
              var nextLineIsSmaller = isSmallerThanWidth(key, lastLine, fontSize);
              var isLastWord = i >= textSplit.length - 1;
              if (nextLineIsSmaller && !isLastWord) {
                  lastLine += " ";
                  continue; // Line
              } else if (!nextLineIsSmaller && !isLastWord) {
                  if (!formObject.multiline) {
                      continue FontSize;
                  } else {
                      if ((textHeight + lineSpacing) * (lineCount + 2) + lineSpacing > height) {
                          // If the Text is higher than the FieldObject
                          continue FontSize;
                      }
                      lastWordInLine = key;
                      // go on
                  }
              } else if (isLastWord) {
                  lastWordInLine = key;
              } else {
                  if (formObject.multiline && (textHeight + lineSpacing) * (lineCount + 2) + lineSpacing > height) {
                      // If the Text is higher than the FieldObject
                      continue FontSize;
                  }
              }

              var line = '';

              for (var x = firstWordInLine; x <= lastWordInLine; x++) {
                  line += textSplit[x] + ' ';
              }

              // Remove last blank
              line = line.substr(line.length - 1) == " " ? line.substr(0, line.length - 1) : line;
              //lastLength -= blankSpace.width;
              lastLength = AcroForm.internal.calculateFontSpace(line, fontSize + "px", font).width;

              // Calculate startX
              switch (formObject.Q) {
                  case 2:
                      // Right justified
                      startX = width - lastLength - borderPadding;
                      break;
                  case 1:
                      // Q = 1 := Text-Alignment: Center
                      startX = (width - lastLength) / 2;
                      break;
                  case 0:
                  default:
                      startX = borderPadding;
                      break;
              }
              text += startX + ' ' + lastY + ' Td\n';
              text += '(' + line + ') Tj\n';
              // reset X in PDF
              text += -startX + ' 0 Td\n';

              // After a Line, adjust y position
              lastY = -(fontSize + lineSpacing);
              lastX = startX;

              // Reset for next iteration step
              lastLength = 0;
              firstWordInLine = lastWordInLine + 1;
              lineCount++;

              lastLine = "";
              continue Line;
          }
          break;
      }

      returnValue.text = text;
      returnValue.fontSize = fontSize;

      return returnValue;
  };

  AcroForm.internal.calculateAppearanceStream = function (formObject) {
      if (formObject.appearanceStreamContent) {
          // If appearanceStream is already set, use it
          return formObject.appearanceStreamContent;
      }

      if (!formObject.V && !formObject.DV) {
          return;
      }

      // else calculate it

      var stream = '';

      var text = formObject.V || formObject.DV;

      var calcRes = AcroForm.internal.calculateX(formObject, text);

      stream += '/Tx BMC\n' + 'q\n' +
      //color + '\n' +
      '/F1 ' + calcRes.fontSize + ' Tf\n' +
      // Text Matrix
      '1 0 0 1 0 0 Tm\n';
      // Begin Text
      stream += 'BT\n';
      stream += calcRes.text;
      // End Text
      stream += 'ET\n';
      stream += 'Q\n' + 'EMC\n';

      var appearanceStreamContent = new AcroForm.createFormXObject(formObject);

      appearanceStreamContent.stream = stream;

      var appearance = {
          N: {
              'Normal': appearanceStreamContent
          }
      };

      return appearanceStreamContent;
  };

  /*
   * Converts the Parameters from x,y,w,h to lowerLeftX, lowerLeftY, upperRightX, upperRightY
   * @param x
   * @param y
   * @param w
   * @param h
   * @returns {*[]}
   */
  AcroForm.internal.calculateCoordinates = function (x, y, w, h) {
      var coordinates = {};

      if (this.internal) {
          var mmtopx = function mmtopx(x) {
              return x * this.internal.scaleFactor;
          };

          if (Array.isArray(x)) {
              x[0] = AcroForm.scale(x[0]);
              x[1] = AcroForm.scale(x[1]);
              x[2] = AcroForm.scale(x[2]);
              x[3] = AcroForm.scale(x[3]);

              coordinates.lowerLeft_X = x[0] || 0;
              coordinates.lowerLeft_Y = mmtopx.call(this, this.internal.pageSize.height) - x[3] - x[1] || 0;
              coordinates.upperRight_X = x[0] + x[2] || 0;
              coordinates.upperRight_Y = mmtopx.call(this, this.internal.pageSize.height) - x[1] || 0;
          } else {
              x = AcroForm.scale(x);
              y = AcroForm.scale(y);
              w = AcroForm.scale(w);
              h = AcroForm.scale(h);
              coordinates.lowerLeft_X = x || 0;
              coordinates.lowerLeft_Y = this.internal.pageSize.height - y || 0;
              coordinates.upperRight_X = x + w || 0;
              coordinates.upperRight_Y = this.internal.pageSize.height - y + h || 0;
          }
      } else {
          // old method, that is fallback, if we can't get the pageheight, the coordinate-system starts from lower left
          if (Array.isArray(x)) {
              coordinates.lowerLeft_X = x[0] || 0;
              coordinates.lowerLeft_Y = x[1] || 0;
              coordinates.upperRight_X = x[0] + x[2] || 0;
              coordinates.upperRight_Y = x[1] + x[3] || 0;
          } else {
              coordinates.lowerLeft_X = x || 0;
              coordinates.lowerLeft_Y = y || 0;
              coordinates.upperRight_X = x + w || 0;
              coordinates.upperRight_Y = y + h || 0;
          }
      }

      return [coordinates.lowerLeft_X, coordinates.lowerLeft_Y, coordinates.upperRight_X, coordinates.upperRight_Y];
  };

  AcroForm.internal.calculateColor = function (r, g, b) {
      var color = new Array(3);
      color.r = r | 0;
      color.g = g | 0;
      color.b = b | 0;
      return color;
  };

  AcroForm.internal.getBitPosition = function (variable, position) {
      variable = variable || 0;
      var bitMask = 1;
      bitMask = bitMask << position - 1;
      return variable | bitMask;
  };

  AcroForm.internal.setBitPosition = function (variable, position, value) {
      variable = variable || 0;
      value = value || 1;

      var bitMask = 1;
      bitMask = bitMask << position - 1;

      if (value == 1) {
          // Set the Bit to 1
          var variable = variable | bitMask;
      } else {
          // Set the Bit to 0
          var variable = variable & ~bitMask;
      }

      return variable;
  };

  /**
   * jsPDF addHTML PlugIn
   * Copyright (c) 2014 Diego Casorran
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
  	'use strict';

  	/**
    * Renders an HTML element to canvas object which added to the PDF
    *
    * This PlugIn requires html2canvas: https://github.com/niklasvh/html2canvas
    *            OR rasterizeHTML: https://github.com/cburgmer/rasterizeHTML.js
    *
    * @public
    * @function
    * @param element {Mixed} HTML Element, or anything supported by html2canvas.
    * @param x {Number} starting X coordinate in jsPDF instance's declared units.
    * @param y {Number} starting Y coordinate in jsPDF instance's declared units.
    * @param options {Object} Additional options, check the code below.
    * @param callback {Function} to call when the rendering has finished.
    *
    * NOTE: Every parameter is optional except 'element' and 'callback', in such
    *       case the image is positioned at 0x0 covering the whole PDF document
    *       size. Ie, to easily take screenshots of webpages saving them to PDF.
    */

  	jsPDFAPI.addHTML = function (element, x, y, options, callback) {
  		'use strict';

  		if (typeof html2canvas === 'undefined' && typeof rasterizeHTML === 'undefined') throw new Error('You need either ' + 'https://github.com/niklasvh/html2canvas' + ' or https://github.com/cburgmer/rasterizeHTML.js');

  		if (typeof x !== 'number') {
  			options = x;
  			callback = y;
  		}

  		if (typeof options === 'function') {
  			callback = options;
  			options = null;
  		}

  		var I = this.internal,
  		    K = I.scaleFactor,
  		    W = I.pageSize.width,
  		    H = I.pageSize.height;

  		options = options || {};
  		options.onrendered = function (obj) {
  			x = parseInt(x) || 0;
  			y = parseInt(y) || 0;
  			var dim = options.dim || {};
  			var h = dim.h || 0;
  			var w = dim.w || Math.min(W, obj.width / K) - x;

  			var format = 'JPEG';
  			if (options.format) format = options.format;

  			if (obj.height > H && options.pagesplit) {
  				var crop = function () {
  					var cy = 0;
  					while (1) {
  						var canvas = document.createElement('canvas');
  						canvas.width = Math.min(W * K, obj.width);
  						canvas.height = Math.min(H * K, obj.height - cy);
  						var ctx = canvas.getContext('2d');
  						ctx.drawImage(obj, 0, cy, obj.width, canvas.height, 0, 0, canvas.width, canvas.height);
  						var args = [canvas, x, cy ? 0 : y, canvas.width / K, canvas.height / K, format, null, 'SLOW'];
  						this.addImage.apply(this, args);
  						cy += canvas.height;
  						if (cy >= obj.height) break;
  						this.addPage();
  					}
  					callback(w, cy, null, args);
  				}.bind(this);
  				if (obj.nodeName === 'CANVAS') {
  					var img = new Image();
  					img.onload = crop;
  					img.src = obj.toDataURL("image/png");
  					obj = img;
  				} else {
  					crop();
  				}
  			} else {
  				var alias = Math.random().toString(35);
  				var args = [obj, x, y, w, h, format, alias, 'SLOW'];

  				this.addImage.apply(this, args);

  				callback(w, h, alias, args);
  			}
  		}.bind(this);

  		if (typeof html2canvas !== 'undefined' && !options.rstz) {
  			return html2canvas(element, options);
  		}

  		if (typeof rasterizeHTML !== 'undefined') {
  			var meth = 'drawDocument';
  			if (typeof element === 'string') {
  				meth = /^http/.test(element) ? 'drawURL' : 'drawHTML';
  			}
  			options.width = options.width || W * K;
  			return rasterizeHTML[meth](element, void 0, options).then(function (r) {
  				options.onrendered(r.image);
  			}, function (e) {
  				callback(null, e);
  			});
  		}

  		return null;
  	};
  })(jsPDF.API);

  /** @preserve
   * jsPDF addImage plugin
   * Copyright (c) 2012 Jason Siefken, https://github.com/siefkenj/
   *               2013 Chris Dowling, https://github.com/gingerchris
   *               2013 Trinh Ho, https://github.com/ineedfat
   *               2013 Edwin Alejandro Perez, https://github.com/eaparango
   *               2013 Norah Smith, https://github.com/burnburnrocket
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 James Robb, https://github.com/jamesbrobb
   *
   * 
   */

  ;(function (jsPDFAPI) {
  	'use strict';

  	var namespace = 'addImage_',
  	    supported_image_types = ['jpeg', 'jpg', 'png'];

  	// Image functionality ported from pdf.js
  	var putImage = function putImage(img) {

  		var objectNumber = this.internal.newObject(),
  		    out = this.internal.write,
  		    putStream = this.internal.putStream;

  		img['n'] = objectNumber;

  		out('<</Type /XObject');
  		out('/Subtype /Image');
  		out('/Width ' + img['w']);
  		out('/Height ' + img['h']);
  		if (img['cs'] === this.color_spaces.INDEXED) {
  			out('/ColorSpace [/Indexed /DeviceRGB '
  			// if an indexed png defines more than one colour with transparency, we've created a smask
  			+ (img['pal'].length / 3 - 1) + ' ' + ('smask' in img ? objectNumber + 2 : objectNumber + 1) + ' 0 R]');
  		} else {
  			out('/ColorSpace /' + img['cs']);
  			if (img['cs'] === this.color_spaces.DEVICE_CMYK) {
  				out('/Decode [1 0 1 0 1 0 1 0]');
  			}
  		}
  		out('/BitsPerComponent ' + img['bpc']);
  		if ('f' in img) {
  			out('/Filter /' + img['f']);
  		}
  		if ('dp' in img) {
  			out('/DecodeParms <<' + img['dp'] + '>>');
  		}
  		if ('trns' in img && img['trns'].constructor == Array) {
  			var trns = '',
  			    i = 0,
  			    len = img['trns'].length;
  			for (; i < len; i++) {
  				trns += img['trns'][i] + ' ' + img['trns'][i] + ' ';
  			}out('/Mask [' + trns + ']');
  		}
  		if ('smask' in img) {
  			out('/SMask ' + (objectNumber + 1) + ' 0 R');
  		}
  		out('/Length ' + img['data'].length + '>>');

  		putStream(img['data']);

  		out('endobj');

  		// Soft mask
  		if ('smask' in img) {
  			var dp = '/Predictor ' + img['p'] + ' /Colors 1 /BitsPerComponent ' + img['bpc'] + ' /Columns ' + img['w'];
  			var smask = { 'w': img['w'], 'h': img['h'], 'cs': 'DeviceGray', 'bpc': img['bpc'], 'dp': dp, 'data': img['smask'] };
  			if ('f' in img) smask.f = img['f'];
  			putImage.call(this, smask);
  		}

  		//Palette
  		if (img['cs'] === this.color_spaces.INDEXED) {

  			this.internal.newObject();
  			//out('<< /Filter / ' + img['f'] +' /Length ' + img['pal'].length + '>>');
  			//putStream(zlib.compress(img['pal']));
  			out('<< /Length ' + img['pal'].length + '>>');
  			putStream(this.arrayBufferToBinaryString(new Uint8Array(img['pal'])));
  			out('endobj');
  		}
  	},
  	    putResourcesCallback = function putResourcesCallback() {
  		var images = this.internal.collections[namespace + 'images'];
  		for (var i in images) {
  			putImage.call(this, images[i]);
  		}
  	},
  	    putXObjectsDictCallback = function putXObjectsDictCallback() {
  		var images = this.internal.collections[namespace + 'images'],
  		    out = this.internal.write,
  		    image;
  		for (var i in images) {
  			image = images[i];
  			out('/I' + image['i'], image['n'], '0', 'R');
  		}
  	},
  	    checkCompressValue = function checkCompressValue(value) {
  		if (value && typeof value === 'string') value = value.toUpperCase();
  		return value in jsPDFAPI.image_compression ? value : jsPDFAPI.image_compression.NONE;
  	},
  	    getImages = function getImages() {
  		var images = this.internal.collections[namespace + 'images'];
  		//first run, so initialise stuff
  		if (!images) {
  			this.internal.collections[namespace + 'images'] = images = {};
  			this.internal.events.subscribe('putResources', putResourcesCallback);
  			this.internal.events.subscribe('putXobjectDict', putXObjectsDictCallback);
  		}

  		return images;
  	},
  	    getImageIndex = function getImageIndex(images) {
  		var imageIndex = 0;

  		if (images) {
  			// this is NOT the first time this method is ran on this instance of jsPDF object.
  			imageIndex = Object.keys ? Object.keys(images).length : function (o) {
  				var i = 0;
  				for (var e in o) {
  					if (o.hasOwnProperty(e)) {
  						i++;
  					}
  				}
  				return i;
  			}(images);
  		}

  		return imageIndex;
  	},
  	    notDefined = function notDefined(value) {
  		return typeof value === 'undefined' || value === null;
  	},
  	    generateAliasFromData = function generateAliasFromData(data) {
  		return typeof data === 'string' && jsPDFAPI.sHashCode(data);
  	},
  	    doesNotSupportImageType = function doesNotSupportImageType(type) {
  		return supported_image_types.indexOf(type) === -1;
  	},
  	    processMethodNotEnabled = function processMethodNotEnabled(type) {
  		return typeof jsPDFAPI['process' + type.toUpperCase()] !== 'function';
  	},
  	    isDOMElement = function isDOMElement(object) {
  		return (typeof object === 'undefined' ? 'undefined' : _typeof(object)) === 'object' && object.nodeType === 1;
  	},
  	    createDataURIFromElement = function createDataURIFromElement(element, format, angle) {

  		//if element is an image which uses data url definition, just return the dataurl
  		if (element.nodeName === 'IMG' && element.hasAttribute('src')) {
  			var src = '' + element.getAttribute('src');
  			if (!angle && src.indexOf('data:image/') === 0) return src;

  			// only if the user doesn't care about a format
  			if (!format && /\.png(?:[?#].*)?$/i.test(src)) format = 'png';
  		}

  		if (element.nodeName === 'CANVAS') {
  			var canvas = element;
  		} else {
  			var canvas = document.createElement('canvas');
  			canvas.width = element.clientWidth || element.width;
  			canvas.height = element.clientHeight || element.height;

  			var ctx = canvas.getContext('2d');
  			if (!ctx) {
  				throw 'addImage requires canvas to be supported by browser.';
  			}
  			if (angle) {
  				var x,
  				    y,
  				    b,
  				    c,
  				    s,
  				    w,
  				    h,
  				    to_radians = Math.PI / 180,
  				    angleInRadians;

  				if ((typeof angle === 'undefined' ? 'undefined' : _typeof(angle)) === 'object') {
  					x = angle.x;
  					y = angle.y;
  					b = angle.bg;
  					angle = angle.angle;
  				}
  				angleInRadians = angle * to_radians;
  				c = Math.abs(Math.cos(angleInRadians));
  				s = Math.abs(Math.sin(angleInRadians));
  				w = canvas.width;
  				h = canvas.height;
  				canvas.width = h * s + w * c;
  				canvas.height = h * c + w * s;

  				if (isNaN(x)) x = canvas.width / 2;
  				if (isNaN(y)) y = canvas.height / 2;

  				ctx.clearRect(0, 0, canvas.width, canvas.height);
  				ctx.fillStyle = b || 'white';
  				ctx.fillRect(0, 0, canvas.width, canvas.height);
  				ctx.save();
  				ctx.translate(x, y);
  				ctx.rotate(angleInRadians);
  				ctx.drawImage(element, -(w / 2), -(h / 2));
  				ctx.rotate(-angleInRadians);
  				ctx.translate(-x, -y);
  				ctx.restore();
  			} else {
  				ctx.drawImage(element, 0, 0, canvas.width, canvas.height);
  			}
  		}
  		return canvas.toDataURL(('' + format).toLowerCase() == 'png' ? 'image/png' : 'image/jpeg');
  	},
  	    checkImagesForAlias = function checkImagesForAlias(alias, images) {
  		var cached_info;
  		if (images) {
  			for (var e in images) {
  				if (alias === images[e].alias) {
  					cached_info = images[e];
  					break;
  				}
  			}
  		}
  		return cached_info;
  	},
  	    determineWidthAndHeight = function determineWidthAndHeight(w, h, info) {
  		if (!w && !h) {
  			w = -96;
  			h = -96;
  		}
  		if (w < 0) {
  			w = -1 * info['w'] * 72 / w / this.internal.scaleFactor;
  		}
  		if (h < 0) {
  			h = -1 * info['h'] * 72 / h / this.internal.scaleFactor;
  		}
  		if (w === 0) {
  			w = h * info['w'] / info['h'];
  		}
  		if (h === 0) {
  			h = w * info['h'] / info['w'];
  		}

  		return [w, h];
  	},
  	    writeImageToPDF = function writeImageToPDF(x, y, w, h, info, index, images) {
  		var dims = determineWidthAndHeight.call(this, w, h, info),
  		    coord = this.internal.getCoordinateString,
  		    vcoord = this.internal.getVerticalCoordinateString;

  		w = dims[0];
  		h = dims[1];

  		images[index] = info;

  		this.internal.write('q', coord(w), '0 0', coord(h) // TODO: check if this should be shifted by vcoord
  		, coord(x), vcoord(y + h), 'cm /I' + info['i'], 'Do Q');
  	};

  	/**
    * COLOR SPACES
    */
  	jsPDFAPI.color_spaces = {
  		DEVICE_RGB: 'DeviceRGB',
  		DEVICE_GRAY: 'DeviceGray',
  		DEVICE_CMYK: 'DeviceCMYK',
  		CAL_GREY: 'CalGray',
  		CAL_RGB: 'CalRGB',
  		LAB: 'Lab',
  		ICC_BASED: 'ICCBased',
  		INDEXED: 'Indexed',
  		PATTERN: 'Pattern',
  		SEPARATION: 'Separation',
  		DEVICE_N: 'DeviceN'
  	};

  	/**
    * DECODE METHODS
    */
  	jsPDFAPI.decode = {
  		DCT_DECODE: 'DCTDecode',
  		FLATE_DECODE: 'FlateDecode',
  		LZW_DECODE: 'LZWDecode',
  		JPX_DECODE: 'JPXDecode',
  		JBIG2_DECODE: 'JBIG2Decode',
  		ASCII85_DECODE: 'ASCII85Decode',
  		ASCII_HEX_DECODE: 'ASCIIHexDecode',
  		RUN_LENGTH_DECODE: 'RunLengthDecode',
  		CCITT_FAX_DECODE: 'CCITTFaxDecode'
  	};

  	/**
    * IMAGE COMPRESSION TYPES
    */
  	jsPDFAPI.image_compression = {
  		NONE: 'NONE',
  		FAST: 'FAST',
  		MEDIUM: 'MEDIUM',
  		SLOW: 'SLOW'
  	};

  	jsPDFAPI.sHashCode = function (str) {
  		return Array.prototype.reduce && str.split("").reduce(function (a, b) {
  			a = (a << 5) - a + b.charCodeAt(0);return a & a;
  		}, 0);
  	};

  	jsPDFAPI.isString = function (object) {
  		return typeof object === 'string';
  	};

  	/**
    * Strips out and returns info from a valid base64 data URI
    * @param {String[dataURI]} a valid data URI of format 'data:[<MIME-type>][;base64],<data>'
    * @returns an Array containing the following
    * [0] the complete data URI
    * [1] <MIME-type>
    * [2] format - the second part of the mime-type i.e 'png' in 'image/png'
    * [4] <data>
    */
  	jsPDFAPI.extractInfoFromBase64DataURI = function (dataURI) {
  		return (/^data:([\w]+?\/([\w]+?));base64,(.+?)$/g.exec(dataURI)
  		);
  	};

  	/**
    * Check to see if ArrayBuffer is supported
    */
  	jsPDFAPI.supportsArrayBuffer = function () {
  		return typeof ArrayBuffer !== 'undefined' && typeof Uint8Array !== 'undefined';
  	};

  	/**
    * Tests supplied object to determine if ArrayBuffer
    * @param {Object[object]}
    */
  	jsPDFAPI.isArrayBuffer = function (object) {
  		if (!this.supportsArrayBuffer()) return false;
  		return object instanceof ArrayBuffer;
  	};

  	/**
    * Tests supplied object to determine if it implements the ArrayBufferView (TypedArray) interface
    * @param {Object[object]}
    */
  	jsPDFAPI.isArrayBufferView = function (object) {
  		if (!this.supportsArrayBuffer()) return false;
  		if (typeof Uint32Array === 'undefined') return false;
  		return object instanceof Int8Array || object instanceof Uint8Array || typeof Uint8ClampedArray !== 'undefined' && object instanceof Uint8ClampedArray || object instanceof Int16Array || object instanceof Uint16Array || object instanceof Int32Array || object instanceof Uint32Array || object instanceof Float32Array || object instanceof Float64Array;
  	};

  	/**
    * Exactly what it says on the tin
    */
  	jsPDFAPI.binaryStringToUint8Array = function (binary_string) {
  		/*
     * not sure how efficient this will be will bigger files. Is there a native method?
     */
  		var len = binary_string.length;
  		var bytes = new Uint8Array(len);
  		for (var i = 0; i < len; i++) {
  			bytes[i] = binary_string.charCodeAt(i);
  		}
  		return bytes;
  	};

  	/**
    * @see this discussion
    * http://stackoverflow.com/questions/6965107/converting-between-strings-and-arraybuffers
    *
    * As stated, i imagine the method below is highly inefficent for large files.
    *
    * Also of note from Mozilla,
    *
    * "However, this is slow and error-prone, due to the need for multiple conversions (especially if the binary data is not actually byte-format data, but, for example, 32-bit integers or floats)."
    *
    * https://developer.mozilla.org/en-US/Add-ons/Code_snippets/StringView
    *
    * Although i'm strugglig to see how StringView solves this issue? Doesn't appear to be a direct method for conversion?
    *
    * Async method using Blob and FileReader could be best, but i'm not sure how to fit it into the flow?
    */
  	jsPDFAPI.arrayBufferToBinaryString = function (buffer) {
  		/*if('TextDecoder' in window){
    	var decoder = new TextDecoder('ascii');
    	return decoder.decode(buffer);
    }*/

  		if (this.isArrayBuffer(buffer)) buffer = new Uint8Array(buffer);

  		var binary_string = '';
  		var len = buffer.byteLength;
  		for (var i = 0; i < len; i++) {
  			binary_string += String.fromCharCode(buffer[i]);
  		}
  		return binary_string;
  		/*
     * Another solution is the method below - convert array buffer straight to base64 and then use atob
     */
  		//return atob(this.arrayBufferToBase64(buffer));
  	};

  	/**
    * Converts an ArrayBuffer directly to base64
    *
    * Taken from here
    *
    * http://jsperf.com/encoding-xhr-image-data/31
    *
    * Need to test if this is a better solution for larger files
    *
    */
  	jsPDFAPI.arrayBufferToBase64 = function (arrayBuffer) {
  		var base64 = '';
  		var encodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  		var bytes = new Uint8Array(arrayBuffer);
  		var byteLength = bytes.byteLength;
  		var byteRemainder = byteLength % 3;
  		var mainLength = byteLength - byteRemainder;

  		var a, b, c, d;
  		var chunk;

  		// Main loop deals with bytes in chunks of 3
  		for (var i = 0; i < mainLength; i = i + 3) {
  			// Combine the three bytes into a single integer
  			chunk = bytes[i] << 16 | bytes[i + 1] << 8 | bytes[i + 2];

  			// Use bitmasks to extract 6-bit segments from the triplet
  			a = (chunk & 16515072) >> 18; // 16515072 = (2^6 - 1) << 18
  			b = (chunk & 258048) >> 12; // 258048   = (2^6 - 1) << 12
  			c = (chunk & 4032) >> 6; // 4032     = (2^6 - 1) << 6
  			d = chunk & 63; // 63       = 2^6 - 1

  			// Convert the raw binary segments to the appropriate ASCII encoding
  			base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d];
  		}

  		// Deal with the remaining bytes and padding
  		if (byteRemainder == 1) {
  			chunk = bytes[mainLength];

  			a = (chunk & 252) >> 2; // 252 = (2^6 - 1) << 2

  			// Set the 4 least significant bits to zero
  			b = (chunk & 3) << 4; // 3   = 2^2 - 1

  			base64 += encodings[a] + encodings[b] + '==';
  		} else if (byteRemainder == 2) {
  			chunk = bytes[mainLength] << 8 | bytes[mainLength + 1];

  			a = (chunk & 64512) >> 10; // 64512 = (2^6 - 1) << 10
  			b = (chunk & 1008) >> 4; // 1008  = (2^6 - 1) << 4

  			// Set the 2 least significant bits to zero
  			c = (chunk & 15) << 2; // 15    = 2^4 - 1

  			base64 += encodings[a] + encodings[b] + encodings[c] + '=';
  		}

  		return base64;
  	};

  	jsPDFAPI.createImageInfo = function (data, wd, ht, cs, bpc, f, imageIndex, alias, dp, trns, pal, smask, p) {
  		var info = {
  			alias: alias,
  			w: wd,
  			h: ht,
  			cs: cs,
  			bpc: bpc,
  			i: imageIndex,
  			data: data
  			// n: objectNumber will be added by putImage code
  		};

  		if (f) info.f = f;
  		if (dp) info.dp = dp;
  		if (trns) info.trns = trns;
  		if (pal) info.pal = pal;
  		if (smask) info.smask = smask;
  		if (p) info.p = p; // predictor parameter for PNG compression

  		return info;
  	};

  	jsPDFAPI.addImage = function (imageData, format, x, y, w, h, alias, compression, rotation) {
  		'use strict';

  		if (typeof format !== 'string') {
  			var tmp = h;
  			h = w;
  			w = y;
  			y = x;
  			x = format;
  			format = tmp;
  		}

  		if ((typeof imageData === 'undefined' ? 'undefined' : _typeof(imageData)) === 'object' && !isDOMElement(imageData) && "imageData" in imageData) {
  			var options = imageData;

  			imageData = options.imageData;
  			format = options.format || format;
  			x = options.x || x || 0;
  			y = options.y || y || 0;
  			w = options.w || w;
  			h = options.h || h;
  			alias = options.alias || alias;
  			compression = options.compression || compression;
  			rotation = options.rotation || options.angle || rotation;
  		}

  		if (isNaN(x) || isNaN(y)) {
  			console.error('jsPDF.addImage: Invalid coordinates', arguments);
  			throw new Error('Invalid coordinates passed to jsPDF.addImage');
  		}

  		var images = getImages.call(this),
  		    info;

  		if (!(info = checkImagesForAlias(imageData, images))) {
  			var dataAsBinaryString;

  			if (isDOMElement(imageData)) imageData = createDataURIFromElement(imageData, format, rotation);

  			if (notDefined(alias)) alias = generateAliasFromData(imageData);

  			if (!(info = checkImagesForAlias(alias, images))) {

  				if (this.isString(imageData)) {

  					var base64Info = this.extractInfoFromBase64DataURI(imageData);

  					if (base64Info) {

  						format = base64Info[2];
  						imageData = atob(base64Info[3]); //convert to binary string
  					} else {

  						if (imageData.charCodeAt(0) === 0x89 && imageData.charCodeAt(1) === 0x50 && imageData.charCodeAt(2) === 0x4e && imageData.charCodeAt(3) === 0x47) format = 'png';
  					}
  				}
  				format = (format || 'JPEG').toLowerCase();

  				if (doesNotSupportImageType(format)) throw new Error('addImage currently only supports formats ' + supported_image_types + ', not \'' + format + '\'');

  				if (processMethodNotEnabled(format)) throw new Error('please ensure that the plugin for \'' + format + '\' support is added');

  				/**
       * need to test if it's more efficient to convert all binary strings
       * to TypedArray - or should we just leave and process as string?
       */
  				if (this.supportsArrayBuffer()) {
  					// no need to convert if imageData is already uint8array
  					if (!(imageData instanceof Uint8Array)) {
  						dataAsBinaryString = imageData;
  						imageData = this.binaryStringToUint8Array(imageData);
  					}
  				}

  				info = this['process' + format.toUpperCase()](imageData, getImageIndex(images), alias, checkCompressValue(compression), dataAsBinaryString);

  				if (!info) throw new Error('An unkwown error occurred whilst processing the image');
  			}
  		}

  		writeImageToPDF.call(this, x, y, w, h, info, info.i, images);

  		return this;
  	};

  	/**
    * JPEG SUPPORT
    **/

  	//takes a string imgData containing the raw bytes of
  	//a jpeg image and returns [width, height]
  	//Algorithm from: http://www.64lines.com/jpeg-width-height
  	var getJpegSize = function getJpegSize(imgData) {
  		'use strict';

  		var width, height, numcomponents;
  		// Verify we have a valid jpeg header 0xff,0xd8,0xff,0xe0,?,?,'J','F','I','F',0x00
  		if (!imgData.charCodeAt(0) === 0xff || !imgData.charCodeAt(1) === 0xd8 || !imgData.charCodeAt(2) === 0xff || !imgData.charCodeAt(3) === 0xe0 || !imgData.charCodeAt(6) === 'J'.charCodeAt(0) || !imgData.charCodeAt(7) === 'F'.charCodeAt(0) || !imgData.charCodeAt(8) === 'I'.charCodeAt(0) || !imgData.charCodeAt(9) === 'F'.charCodeAt(0) || !imgData.charCodeAt(10) === 0x00) {
  			throw new Error('getJpegSize requires a binary string jpeg file');
  		}
  		var blockLength = imgData.charCodeAt(4) * 256 + imgData.charCodeAt(5);
  		var i = 4,
  		    len = imgData.length;
  		while (i < len) {
  			i += blockLength;
  			if (imgData.charCodeAt(i) !== 0xff) {
  				throw new Error('getJpegSize could not find the size of the image');
  			}
  			if (imgData.charCodeAt(i + 1) === 0xc0 || //(SOF) Huffman  - Baseline DCT
  			imgData.charCodeAt(i + 1) === 0xc1 || //(SOF) Huffman  - Extended sequential DCT
  			imgData.charCodeAt(i + 1) === 0xc2 || // Progressive DCT (SOF2)
  			imgData.charCodeAt(i + 1) === 0xc3 || // Spatial (sequential) lossless (SOF3)
  			imgData.charCodeAt(i + 1) === 0xc4 || // Differential sequential DCT (SOF5)
  			imgData.charCodeAt(i + 1) === 0xc5 || // Differential progressive DCT (SOF6)
  			imgData.charCodeAt(i + 1) === 0xc6 || // Differential spatial (SOF7)
  			imgData.charCodeAt(i + 1) === 0xc7) {
  				height = imgData.charCodeAt(i + 5) * 256 + imgData.charCodeAt(i + 6);
  				width = imgData.charCodeAt(i + 7) * 256 + imgData.charCodeAt(i + 8);
  				numcomponents = imgData.charCodeAt(i + 9);
  				return [width, height, numcomponents];
  			} else {
  				i += 2;
  				blockLength = imgData.charCodeAt(i) * 256 + imgData.charCodeAt(i + 1);
  			}
  		}
  	},
  	    getJpegSizeFromBytes = function getJpegSizeFromBytes(data) {

  		var hdr = data[0] << 8 | data[1];

  		if (hdr !== 0xFFD8) throw new Error('Supplied data is not a JPEG');

  		var len = data.length,
  		    block = (data[4] << 8) + data[5],
  		    pos = 4,
  		    bytes,
  		    width,
  		    height,
  		    numcomponents;

  		while (pos < len) {
  			pos += block;
  			bytes = readBytes(data, pos);
  			block = (bytes[2] << 8) + bytes[3];
  			if ((bytes[1] === 0xC0 || bytes[1] === 0xC2) && bytes[0] === 0xFF && block > 7) {
  				bytes = readBytes(data, pos + 5);
  				width = (bytes[2] << 8) + bytes[3];
  				height = (bytes[0] << 8) + bytes[1];
  				numcomponents = bytes[4];
  				return { width: width, height: height, numcomponents: numcomponents };
  			}

  			pos += 2;
  		}

  		throw new Error('getJpegSizeFromBytes could not find the size of the image');
  	},
  	    readBytes = function readBytes(data, offset) {
  		return data.subarray(offset, offset + 5);
  	};

  	jsPDFAPI.processJPEG = function (data, index, alias, compression, dataAsBinaryString) {
  		'use strict';

  		var colorSpace = this.color_spaces.DEVICE_RGB,
  		    filter = this.decode.DCT_DECODE,
  		    bpc = 8,
  		    dims;

  		if (this.isString(data)) {
  			dims = getJpegSize(data);
  			return this.createImageInfo(data, dims[0], dims[1], dims[3] == 1 ? this.color_spaces.DEVICE_GRAY : colorSpace, bpc, filter, index, alias);
  		}

  		if (this.isArrayBuffer(data)) data = new Uint8Array(data);

  		if (this.isArrayBufferView(data)) {

  			dims = getJpegSizeFromBytes(data);

  			// if we already have a stored binary string rep use that
  			data = dataAsBinaryString || this.arrayBufferToBinaryString(data);

  			return this.createImageInfo(data, dims.width, dims.height, dims.numcomponents == 1 ? this.color_spaces.DEVICE_GRAY : colorSpace, bpc, filter, index, alias);
  		}

  		return null;
  	};

  	jsPDFAPI.processJPG = function () /*data, index, alias, compression, dataAsBinaryString*/{
  		return this.processJPEG.apply(this, arguments);
  	};
  })(jsPDF.API);

  /**
   * jsPDF Annotations PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * There are many types of annotations in a PDF document. Annotations are placed
   * on a page at a particular location. They are not 'attached' to an object.
   * <br />
   * This plugin current supports <br />
   * <li> Goto Page (set pageNumber and top in options)
   * <li> Goto Name (set name and top in options)
   * <li> Goto URL (set url in options)
   * <p>
   * 	The destination magnification factor can also be specified when goto is a page number or a named destination. (see documentation below)
   *  (set magFactor in options).  XYZ is the default.
   * </p>
   * <p>
   *  Links, Text, Popup, and FreeText are supported.
   * </p>
   * <p>
   * Options In PDF spec Not Implemented Yet
   * <li> link border
   * <li> named target
   * <li> page coordinates
   * <li> destination page scaling and layout
   * <li> actions other than URL and GotoPage
   * <li> background / hover actions
   * </p>
   */

  /*
      Destination Magnification Factors
      See PDF 1.3 Page 386 for meanings and options

      [supported]
  	XYZ (options; left top zoom)
  	Fit (no options)
  	FitH (options: top)
  	FitV (options: left)

  	[not supported]
  	FitR
  	FitB
  	FitBH
  	FitBV
   */

  (function (jsPDFAPI) {
  	'use strict';

  	var annotationPlugin = {

  		/**
     * An array of arrays, indexed by <em>pageNumber</em>.
     */
  		annotations: [],

  		f2: function f2(number) {
  			return number.toFixed(2);
  		},

  		notEmpty: function notEmpty(obj) {
  			if (typeof obj != 'undefined') {
  				if (obj != '') {
  					return true;
  				}
  			}
  		}
  	};

  	jsPDF.API.annotationPlugin = annotationPlugin;

  	jsPDF.API.events.push(['addPage', function (info) {
  		this.annotationPlugin.annotations[info.pageNumber] = [];
  	}]);

  	jsPDFAPI.events.push(['putPage', function (info) {
  		//TODO store annotations in pageContext so reorder/remove will not affect them.
  		var pageAnnos = this.annotationPlugin.annotations[info.pageNumber];

  		var found = false;
  		for (var a = 0; a < pageAnnos.length && !found; a++) {
  			var anno = pageAnnos[a];
  			switch (anno.type) {
  				case 'link':
  					if (annotationPlugin.notEmpty(anno.options.url) || annotationPlugin.notEmpty(anno.options.pageNumber)) {
  						found = true;
  						break;
  					}
  				case 'reference':
  				case 'text':
  				case 'freetext':
  					found = true;
  					break;
  			}
  		}
  		if (found == false) {
  			return;
  		}

  		this.internal.write("/Annots [");
  		var f2 = this.annotationPlugin.f2;
  		var k = this.internal.scaleFactor;
  		var pageHeight = this.internal.pageSize.height;
  		var pageInfo = this.internal.getPageInfo(info.pageNumber);
  		for (var a = 0; a < pageAnnos.length; a++) {
  			var anno = pageAnnos[a];

  			switch (anno.type) {
  				case 'reference':
  					// References to Widget Anotations (for AcroForm Fields)
  					this.internal.write(' ' + anno.object.objId + ' 0 R ');
  					break;
  				case 'text':
  					// Create a an object for both the text and the popup
  					var objText = this.internal.newAdditionalObject();
  					var objPopup = this.internal.newAdditionalObject();

  					var title = anno.title || 'Note';
  					var rect = "/Rect [" + f2(anno.bounds.x * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + " " + f2((anno.bounds.x + anno.bounds.w) * k) + " " + f2((pageHeight - anno.bounds.y) * k) + "] ";
  					line = '<</Type /Annot /Subtype /' + 'Text' + ' ' + rect + '/Contents (' + anno.contents + ')';
  					line += ' /Popup ' + objPopup.objId + " 0 R";
  					line += ' /P ' + pageInfo.objId + " 0 R";
  					line += ' /T (' + title + ') >>';
  					objText.content = line;

  					var parent = objText.objId + ' 0 R';
  					var popoff = 30;
  					var rect = "/Rect [" + f2((anno.bounds.x + popoff) * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + " " + f2((anno.bounds.x + anno.bounds.w + popoff) * k) + " " + f2((pageHeight - anno.bounds.y) * k) + "] ";
  					//var rect2 = "/Rect [" + f2(anno.bounds.x * k) + " " + f2((pageHeight - anno.bounds.y) * k) + " " + f2(anno.bounds.x + anno.bounds.w * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + "] ";
  					line = '<</Type /Annot /Subtype /' + 'Popup' + ' ' + rect + ' /Parent ' + parent;
  					if (anno.open) {
  						line += ' /Open true';
  					}
  					line += ' >>';
  					objPopup.content = line;

  					this.internal.write(objText.objId, '0 R', objPopup.objId, '0 R');

  					break;
  				case 'freetext':
  					var rect = "/Rect [" + f2(anno.bounds.x * k) + " " + f2((pageHeight - anno.bounds.y) * k) + " " + f2(anno.bounds.x + anno.bounds.w * k) + " " + f2(pageHeight - (anno.bounds.y + anno.bounds.h) * k) + "] ";
  					var color = anno.color || '#000000';
  					line = '<</Type /Annot /Subtype /' + 'FreeText' + ' ' + rect + '/Contents (' + anno.contents + ')';
  					line += ' /DS(font: Helvetica,sans-serif 12.0pt; text-align:left; color:#' + color + ')';
  					line += ' /Border [0 0 0]';
  					line += ' >>';
  					this.internal.write(line);
  					break;
  				case 'link':
  					if (anno.options.name) {
  						var loc = this.annotations._nameMap[anno.options.name];
  						anno.options.pageNumber = loc.page;
  						anno.options.top = loc.y;
  					} else {
  						if (!anno.options.top) {
  							anno.options.top = 0;
  						}
  					}

  					//var pageHeight = this.internal.pageSize.height * this.internal.scaleFactor;
  					var rect = "/Rect [" + f2(anno.x * k) + " " + f2((pageHeight - anno.y) * k) + " " + f2(anno.x + anno.w * k) + " " + f2(pageHeight - (anno.y + anno.h) * k) + "] ";

  					var line = '';
  					if (anno.options.url) {
  						line = '<</Type /Annot /Subtype /Link ' + rect + '/Border [0 0 0] /A <</S /URI /URI (' + anno.options.url + ') >>';
  					} else if (anno.options.pageNumber) {
  						// first page is 0
  						var info = this.internal.getPageInfo(anno.options.pageNumber);
  						line = '<</Type /Annot /Subtype /Link ' + rect + '/Border [0 0 0] /Dest [' + info.objId + " 0 R";
  						anno.options.magFactor = anno.options.magFactor || "XYZ";
  						switch (anno.options.magFactor) {
  							case 'Fit':
  								line += ' /Fit]';
  								break;
  							case 'FitH':
  								//anno.options.top = anno.options.top || f2(pageHeight * k);
  								line += ' /FitH ' + anno.options.top + ']';
  								break;
  							case 'FitV':
  								anno.options.left = anno.options.left || 0;
  								line += ' /FitV ' + anno.options.left + ']';
  								break;
  							case 'XYZ':
  							default:
  								var top = f2((pageHeight - anno.options.top) * k); // || f2(pageHeight * k);
  								anno.options.left = anno.options.left || 0;
  								// 0 or null zoom will not change zoom factor
  								if (typeof anno.options.zoom === 'undefined') {
  									anno.options.zoom = 0;
  								}
  								line += ' /XYZ ' + anno.options.left + ' ' + top + ' ' + anno.options.zoom + ']';
  								break;
  						}
  					} else {
  						// TODO error - should not be here
  					}
  					if (line != '') {
  						line += " >>";
  						this.internal.write(line);
  					}
  					break;
  			}
  		}
  		this.internal.write("]");
  	}]);

  	jsPDFAPI.createAnnotation = function (options) {
  		switch (options.type) {
  			case 'link':
  				this.link(options.bounds.x, options.bounds.y, options.bounds.w, options.bounds.h, options);
  				break;
  			case 'text':
  			case 'freetext':
  				this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push(options);
  				break;
  		}
  	};

  	/**
    * valid options
    * <li> pageNumber or url [required]
    * <p>If pageNumber is specified, top and zoom may also be specified</p>
    */
  	jsPDFAPI.link = function (x, y, w, h, options) {
  		'use strict';

  		this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push({
  			x: x,
  			y: y,
  			w: w,
  			h: h,
  			options: options,
  			type: 'link'
  		});
  	};

  	/**
    * valid options
    * <li> pageNumber or url [required]
    * <p>If pageNumber is specified, top and zoom may also be specified</p>
    */
  	jsPDFAPI.link = function (x, y, w, h, options) {
  		'use strict';

  		this.annotationPlugin.annotations[this.internal.getCurrentPageInfo().pageNumber].push({
  			x: x,
  			y: y,
  			w: w,
  			h: h,
  			options: options,
  			type: 'link'
  		});
  	};

  	/**
    * Currently only supports single line text.
    * Returns the width of the text/link
    */
  	jsPDFAPI.textWithLink = function (text, x, y, options) {
  		'use strict';

  		var width = this.getTextWidth(text);
  		var height = this.internal.getLineHeight();
  		this.text(text, x, y);
  		//TODO We really need the text baseline height to do this correctly.
  		// Or ability to draw text on top, bottom, center, or baseline.
  		y += height * .2;
  		this.link(x, y - height, width, height, options);
  		return width;
  	};

  	//TODO move into external library
  	jsPDFAPI.getTextWidth = function (text) {
  		'use strict';

  		var fontSize = this.internal.getFontSize();
  		var txtWidth = this.getStringUnitWidth(text) * fontSize / this.internal.scaleFactor;
  		return txtWidth;
  	};

  	//TODO move into external library
  	jsPDFAPI.getLineHeight = function () {
  		return this.internal.getLineHeight();
  	};

  	return this;
  })(jsPDF.API);

  /**
   * jsPDF Autoprint Plugin
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.autoPrint = function () {
  		'use strict';

  		var refAutoPrintTag;

  		this.internal.events.subscribe('postPutResources', function () {
  			refAutoPrintTag = this.internal.newObject();
  			this.internal.write("<< /S/Named /Type/Action /N/Print >>", "endobj");
  		});

  		this.internal.events.subscribe("putCatalog", function () {
  			this.internal.write("/OpenAction " + refAutoPrintTag + " 0" + " R");
  		});
  		return this;
  	};
  })(jsPDF.API);

  /**
   * jsPDF Canvas PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * This plugin mimics the HTML5 Canvas
   * 
   * The goal is to provide a way for current canvas users to print directly to a PDF.
   */

  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.events.push(['initialized', function () {
  		this.canvas.pdf = this;
  	}]);

  	jsPDFAPI.canvas = {
  		getContext: function getContext(name) {
  			this.pdf.context2d._canvas = this;
  			return this.pdf.context2d;
  		},
  		style: {}
  	};

  	Object.defineProperty(jsPDFAPI.canvas, 'width', {
  		get: function get() {
  			return this._width;
  		},
  		set: function set(value) {
  			this._width = value;
  			this.getContext('2d').pageWrapX = value + 1;
  		}
  	});

  	Object.defineProperty(jsPDFAPI.canvas, 'height', {
  		get: function get() {
  			return this._height;
  		},
  		set: function set(value) {
  			this._height = value;
  			this.getContext('2d').pageWrapY = value + 1;
  		}
  	});

  	return this;
  })(jsPDF.API);

  /** ====================================================================
   * jsPDF Cell plugin
   * Copyright (c) 2013 Youssef Beddad, youssef.beddad@gmail.com
   *               2013 Eduardo Menezes de Morais, eduardo.morais@usp.br
   *               2013 Lee Driscoll, https://github.com/lsdriscoll
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 James Hall, james@parall.ax
   *               2014 Diego Casorran, https://github.com/diegocr
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
      'use strict';
      /*jslint browser:true */
      /*global document: false, jsPDF */

      var fontName,
          fontSize,
          fontStyle,
          padding = 3,
          margin = 13,
          headerFunction,
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined },
          pages = 1,
          setLastCellPosition = function setLastCellPosition(x, y, w, h, ln) {
          lastCellPos = { 'x': x, 'y': y, 'w': w, 'h': h, 'ln': ln };
      },
          getLastCellPosition = function getLastCellPosition() {
          return lastCellPos;
      },
          NO_MARGINS = { left: 0, top: 0, bottom: 0 };

      jsPDFAPI.setHeaderFunction = function (func) {
          headerFunction = func;
      };

      jsPDFAPI.getTextDimensions = function (txt) {
          fontName = this.internal.getFont().fontName;
          fontSize = this.table_font_size || this.internal.getFontSize();
          fontStyle = this.internal.getFont().fontStyle;
          // 1 pixel = 0.264583 mm and 1 mm = 72/25.4 point
          var px2pt = 0.264583 * 72 / 25.4,
              dimensions,
              text;

          text = document.createElement('font');
          text.id = "jsPDFCell";

          try {
              text.style.fontStyle = fontStyle;
          } catch (e) {
              text.style.fontWeight = fontStyle;
          }

          text.style.fontName = fontName;
          text.style.fontSize = fontSize + 'pt';
          try {
              text.textContent = txt;
          } catch (e) {
              text.innerText = txt;
          }

          document.body.appendChild(text);

          dimensions = { w: (text.offsetWidth + 1) * px2pt, h: (text.offsetHeight + 1) * px2pt };

          document.body.removeChild(text);

          return dimensions;
      };

      jsPDFAPI.cellAddPage = function () {
          var margins = this.margins || NO_MARGINS;

          this.addPage();

          setLastCellPosition(margins.left, margins.top, undefined, undefined);
          //setLastCellPosition(undefined, undefined, undefined, undefined, undefined);
          pages += 1;
      };

      jsPDFAPI.cellInitialize = function () {
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined };
          pages = 1;
      };

      jsPDFAPI.cell = function (x, y, w, h, txt, ln, align) {
          var curCell = getLastCellPosition();
          var pgAdded = false;

          // If this is not the first cell, we must change its position
          if (curCell.ln !== undefined) {
              if (curCell.ln === ln) {
                  //Same line
                  x = curCell.x + curCell.w;
                  y = curCell.y;
              } else {
                  //New line
                  var margins = this.margins || NO_MARGINS;
                  if (curCell.y + curCell.h + h + margin >= this.internal.pageSize.height - margins.bottom) {
                      this.cellAddPage();
                      pgAdded = true;
                      if (this.printHeaders && this.tableHeaderRow) {
                          this.printHeaderRow(ln, true);
                      }
                  }
                  //We ignore the passed y: the lines may have different heights
                  y = getLastCellPosition().y + getLastCellPosition().h;
                  if (pgAdded) y = margin + 10;
              }
          }

          if (txt[0] !== undefined) {
              if (this.printingHeaderRow) {
                  this.rect(x, y, w, h, 'FD');
              } else {
                  this.rect(x, y, w, h);
              }
              if (align === 'right') {
                  if (!(txt instanceof Array)) {
                      txt = [txt];
                  }
                  for (var i = 0; i < txt.length; i++) {
                      var currentLine = txt[i];
                      var textSize = this.getStringUnitWidth(currentLine) * this.internal.getFontSize();
                      this.text(currentLine, x + w - textSize - padding, y + this.internal.getLineHeight() * (i + 1));
                  }
              } else {
                  this.text(txt, x + padding, y + this.internal.getLineHeight());
              }
          }
          setLastCellPosition(x, y, w, h, ln);
          return this;
      };

      /**
       * Return the maximum value from an array
       * @param array
       * @param comparisonFn
       * @returns {*}
       */
      jsPDFAPI.arrayMax = function (array, comparisonFn) {
          var max = array[0],
              i,
              ln,
              item;

          for (i = 0, ln = array.length; i < ln; i += 1) {
              item = array[i];

              if (comparisonFn) {
                  if (comparisonFn(max, item) === -1) {
                      max = item;
                  }
              } else {
                  if (item > max) {
                      max = item;
                  }
              }
          }

          return max;
      };

      /**
       * Create a table from a set of data.
       * @param {Integer} [x] : left-position for top-left corner of table
       * @param {Integer} [y] top-position for top-left corner of table
       * @param {Object[]} [data] As array of objects containing key-value pairs corresponding to a row of data.
       * @param {String[]} [headers] Omit or null to auto-generate headers at a performance cost
        * @param {Object} [config.printHeaders] True to print column headers at the top of every page
       * @param {Object} [config.autoSize] True to dynamically set the column widths to match the widest cell value
       * @param {Object} [config.margins] margin values for left, top, bottom, and width
       * @param {Object} [config.fontSize] Integer fontSize to use (optional)
       */

      jsPDFAPI.table = function (x, y, data, headers, config) {
          if (!data) {
              throw 'No data for PDF table';
          }

          var headerNames = [],
              headerPrompts = [],
              header,
              i,
              ln,
              cln,
              columnMatrix = {},
              columnWidths = {},
              columnData,
              column,
              columnMinWidths = [],
              j,
              tableHeaderConfigs = [],
              model,
              jln,
              func,


          //set up defaults. If a value is provided in config, defaults will be overwritten:
          autoSize = false,
              printHeaders = true,
              fontSize = 12,
              margins = NO_MARGINS;

          margins.width = this.internal.pageSize.width;

          if (config) {
              //override config defaults if the user has specified non-default behavior:
              if (config.autoSize === true) {
                  autoSize = true;
              }
              if (config.printHeaders === false) {
                  printHeaders = false;
              }
              if (config.fontSize) {
                  fontSize = config.fontSize;
              }
              if (config.css && typeof config.css['font-size'] !== "undefined") {
                  fontSize = config.css['font-size'] * 16;
              }
              if (config.margins) {
                  margins = config.margins;
              }
          }

          /**
           * @property {Number} lnMod
           * Keep track of the current line number modifier used when creating cells
           */
          this.lnMod = 0;
          lastCellPos = { x: undefined, y: undefined, w: undefined, h: undefined, ln: undefined }, pages = 1;

          this.printHeaders = printHeaders;
          this.margins = margins;
          this.setFontSize(fontSize);
          this.table_font_size = fontSize;

          // Set header values
          if (headers === undefined || headers === null) {
              // No headers defined so we derive from data
              headerNames = Object.keys(data[0]);
          } else if (headers[0] && typeof headers[0] !== 'string') {
              var px2pt = 0.264583 * 72 / 25.4;

              // Split header configs into names and prompts
              for (i = 0, ln = headers.length; i < ln; i += 1) {
                  header = headers[i];
                  headerNames.push(header.name);
                  headerPrompts.push(header.prompt);
                  columnWidths[header.name] = header.width * px2pt;
              }
          } else {
              headerNames = headers;
          }

          if (autoSize) {
              // Create a matrix of columns e.g., {column_title: [row1_Record, row2_Record]}
              func = function func(rec) {
                  return rec[header];
              };

              for (i = 0, ln = headerNames.length; i < ln; i += 1) {
                  header = headerNames[i];

                  columnMatrix[header] = data.map(func);

                  // get header width
                  columnMinWidths.push(this.getTextDimensions(headerPrompts[i] || header).w);
                  column = columnMatrix[header];

                  // get cell widths
                  for (j = 0, cln = column.length; j < cln; j += 1) {
                      columnData = column[j];
                      columnMinWidths.push(this.getTextDimensions(columnData).w);
                  }

                  // get final column width
                  columnWidths[header] = jsPDFAPI.arrayMax(columnMinWidths);

                  //have to reset
                  columnMinWidths = [];
              }
          }

          // -- Construct the table

          if (printHeaders) {
              var lineHeight = this.calculateLineHeight(headerNames, columnWidths, headerPrompts.length ? headerPrompts : headerNames);

              // Construct the header row
              for (i = 0, ln = headerNames.length; i < ln; i += 1) {
                  header = headerNames[i];
                  tableHeaderConfigs.push([x, y, columnWidths[header], lineHeight, String(headerPrompts.length ? headerPrompts[i] : header)]);
              }

              // Store the table header config
              this.setTableHeaderRow(tableHeaderConfigs);

              // Print the header for the start of the table
              this.printHeaderRow(1, false);
          }

          // Construct the data rows
          for (i = 0, ln = data.length; i < ln; i += 1) {
              var lineHeight;
              model = data[i];
              lineHeight = this.calculateLineHeight(headerNames, columnWidths, model);

              for (j = 0, jln = headerNames.length; j < jln; j += 1) {
                  header = headerNames[j];
                  this.cell(x, y, columnWidths[header], lineHeight, model[header], i + 2, header.align);
              }
          }
          this.lastCellPos = lastCellPos;
          this.table_x = x;
          this.table_y = y;
          return this;
      };
      /**
       * Calculate the height for containing the highest column
       * @param {String[]} headerNames is the header, used as keys to the data
       * @param {Integer[]} columnWidths is size of each column
       * @param {Object[]} model is the line of data we want to calculate the height of
       */
      jsPDFAPI.calculateLineHeight = function (headerNames, columnWidths, model) {
          var header,
              lineHeight = 0;
          for (var j = 0; j < headerNames.length; j++) {
              header = headerNames[j];
              model[header] = this.splitTextToSize(String(model[header]), columnWidths[header] - padding);
              var h = this.internal.getLineHeight() * model[header].length + padding;
              if (h > lineHeight) lineHeight = h;
          }
          return lineHeight;
      };

      /**
       * Store the config for outputting a table header
       * @param {Object[]} config
       * An array of cell configs that would define a header row: Each config matches the config used by jsPDFAPI.cell
       * except the ln parameter is excluded
       */
      jsPDFAPI.setTableHeaderRow = function (config) {
          this.tableHeaderRow = config;
      };

      /**
       * Output the store header row
       * @param lineNumber The line number to output the header at
       */
      jsPDFAPI.printHeaderRow = function (lineNumber, new_page) {
          if (!this.tableHeaderRow) {
              throw 'Property tableHeaderRow does not exist.';
          }

          var tableHeaderCell, tmpArray, i, ln;

          this.printingHeaderRow = true;
          if (headerFunction !== undefined) {
              var position = headerFunction(this, pages);
              setLastCellPosition(position[0], position[1], position[2], position[3], -1);
          }
          this.setFontStyle('bold');
          var tempHeaderConf = [];
          for (i = 0, ln = this.tableHeaderRow.length; i < ln; i += 1) {
              this.setFillColor(200, 200, 200);

              tableHeaderCell = this.tableHeaderRow[i];
              if (new_page) {
                  this.margins.top = margin;
                  tableHeaderCell[1] = this.margins && this.margins.top || 0;
                  tempHeaderConf.push(tableHeaderCell);
              }
              tmpArray = [].concat(tableHeaderCell);
              this.cell.apply(this, tmpArray.concat(lineNumber));
          }
          if (tempHeaderConf.length > 0) {
              this.setTableHeaderRow(tempHeaderConf);
          }
          this.setFontStyle('normal');
          this.printingHeaderRow = false;
      };
  })(jsPDF.API);

  /**
   * jsPDF Context2D PlugIn Copyright (c) 2014 Steven Spungin (TwelveTone LLC) steven@twelvetone.tv
   *
   * Licensed under the MIT License. http://opensource.org/licenses/mit-license
   */

  /**
   * This plugin mimics the HTML5 Canvas's context2d.
   *
   * The goal is to provide a way for current canvas implementations to print directly to a PDF.
   */

  /**
   * TODO implement stroke opacity (refactor from fill() method )
   * TODO transform angle and radii parameters
   */

  /**
   * require('jspdf.js'); require('lib/css_colors.js');
   */

  (function (jsPDFAPI) {
      'use strict';

      jsPDFAPI.events.push(['initialized', function () {
          this.context2d.pdf = this;
          this.context2d.internal.pdf = this;
          this.context2d.ctx = new context();
          this.context2d.ctxStack = [];
          this.context2d.path = [];
      }]);

      jsPDFAPI.context2d = {
          pageWrapXEnabled: false,
          pageWrapYEnabled: false,
          pageWrapX: 9999999,
          pageWrapY: 9999999,
          ctx: new context(),
          f2: function f2(number) {
              return number.toFixed(2);
          },

          fillRect: function fillRect(x, y, w, h) {
              if (this._isFillTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "f");
          },

          strokeRect: function strokeRect(x, y, w, h) {
              if (this._isStrokeTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "s");
          },

          clearRect: function clearRect(x, y, w, h) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              this.save();
              this.setFillStyle('#ffffff');
              //TODO This is hack to fill with white.
              this.pdf.rect(xRect.x, xRect.y, xRect.w, xRect.h, "f");
              this.restore();
          },

          save: function save() {
              this.ctx._fontSize = this.pdf.internal.getFontSize();
              var ctx = new context();
              ctx.copy(this.ctx);
              this.ctxStack.push(this.ctx);
              this.ctx = ctx;
          },

          restore: function restore() {
              this.ctx = this.ctxStack.pop();
              this.setFillStyle(this.ctx.fillStyle);
              this.setStrokeStyle(this.ctx.strokeStyle);
              this.setFont(this.ctx.font);
              this.pdf.setFontSize(this.ctx._fontSize);
              this.setLineCap(this.ctx.lineCap);
              this.setLineWidth(this.ctx.lineWidth);
              this.setLineJoin(this.ctx.lineJoin);
          },

          rect: function rect(x, y, w, h) {
              this.moveTo(x, y);
              this.lineTo(x + w, y);
              this.lineTo(x + w, y + h);
              this.lineTo(x, y + h);
              this.lineTo(x, y); //TODO not needed
              this.closePath();
          },

          beginPath: function beginPath() {
              this.path = [];
          },

          closePath: function closePath() {
              this.path.push({
                  type: 'close'
              });
          },

          _getRgba: function _getRgba(style) {
              // get the decimal values of r, g, and b;
              var rgba = {};

              if (this.internal.rxTransparent.test(style)) {
                  rgba.r = 0;
                  rgba.g = 0;
                  rgba.b = 0;
                  rgba.a = 0;
              } else {
                  var m = this.internal.rxRgb.exec(style);
                  if (m != null) {
                      rgba.r = parseInt(m[1]);
                      rgba.g = parseInt(m[2]);
                      rgba.b = parseInt(m[3]);
                      rgba.a = 1;
                  } else {
                      m = this.internal.rxRgba.exec(style);
                      if (m != null) {
                          rgba.r = parseInt(m[1]);
                          rgba.g = parseInt(m[2]);
                          rgba.b = parseInt(m[3]);
                          rgba.a = parseFloat(m[4]);
                      } else {
                          rgba.a = 1;
                          if (style.charAt(0) != '#') {
                              style = CssColors.colorNameToHex(style);
                              if (!style) {
                                  style = '#000000';
                              }
                          } else {}

                          if (style.length === 4) {
                              rgba.r = style.substring(1, 2);
                              rgba.r += r;
                              rgba.g = style.substring(2, 3);
                              rgba.g += g;
                              rgba.b = style.substring(3, 4);
                              rgba.b += b;
                          } else {
                              rgba.r = style.substring(1, 3);
                              rgba.g = style.substring(3, 5);
                              rgba.b = style.substring(5, 7);
                          }
                          rgba.r = parseInt(rgba.r, 16);
                          rgba.g = parseInt(rgba.g, 16);
                          rgba.b = parseInt(rgba.b, 16);
                      }
                  }
              }
              rgba.style = style;
              return rgba;
          },

          setFillStyle: function setFillStyle(style) {
              // get the decimal values of r, g, and b;
              var r, g, b, a;

              if (this.internal.rxTransparent.test(style)) {
                  r = 0;
                  g = 0;
                  b = 0;
                  a = 0;
              } else {
                  var m = this.internal.rxRgb.exec(style);
                  if (m != null) {
                      r = parseInt(m[1]);
                      g = parseInt(m[2]);
                      b = parseInt(m[3]);
                      a = 1;
                  } else {
                      m = this.internal.rxRgba.exec(style);
                      if (m != null) {
                          r = parseInt(m[1]);
                          g = parseInt(m[2]);
                          b = parseInt(m[3]);
                          a = parseFloat(m[4]);
                      } else {
                          a = 1;
                          if (style.charAt(0) != '#') {
                              style = CssColors.colorNameToHex(style);
                              if (!style) {
                                  style = '#000000';
                              }
                          } else {}

                          if (style.length === 4) {
                              r = style.substring(1, 2);
                              r += r;
                              g = style.substring(2, 3);
                              g += g;
                              b = style.substring(3, 4);
                              b += b;
                          } else {
                              r = style.substring(1, 3);
                              g = style.substring(3, 5);
                              b = style.substring(5, 7);
                          }
                          r = parseInt(r, 16);
                          g = parseInt(g, 16);
                          b = parseInt(b, 16);
                      }
                  }
              }

              this.ctx.fillStyle = style;
              this.ctx._isFillTransparent = a == 0;
              this.ctx._fillOpacity = a;

              this.pdf.setFillColor(r, g, b, {
                  a: a
              });
              this.pdf.setTextColor(r, g, b, {
                  a: a
              });
          },

          setStrokeStyle: function setStrokeStyle(style) {
              var rgba = this._getRgba(style);

              this.ctx.strokeStyle = rgba.style;
              this.ctx._isStrokeTransparent = rgba.a == 0;
              this.ctx._strokeOpacity = rgba.a;

              //TODO jsPDF to handle rgba
              if (rgba.a === 0) {
                  this.pdf.setDrawColor(255, 255, 255);
              } else if (rgba.a === 1) {
                  this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b);
              } else {
                  //this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b, {a: rgba.a});
                  this.pdf.setDrawColor(rgba.r, rgba.g, rgba.b);
              }
          },

          fillText: function fillText(text, x, y, maxWidth) {
              if (this._isFillTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              var rads = this._matrix_rotation(this.ctx._transform);
              var degs = rads * 57.2958;

              //TODO only push the clip if it has not been applied to the current PDF context
              if (this.ctx._clip_path.length > 0) {
                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");
                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(null, true);
                  this.ctx._clip_path = this.path;
                  this.path = origPath;
              }

              this.pdf.text(text, x, this._getBaseline(y), null, degs);

              if (this.ctx._clip_path.length > 0) {
                  lines.push('Q');
              }
          },

          strokeText: function strokeText(text, x, y, maxWidth) {
              if (this._isStrokeTransparent()) {
                  return;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              var rads = this._matrix_rotation(this.ctx._transform);
              var degs = rads * 57.2958;

              //TODO only push the clip if it has not been applied to the current PDF context
              if (this.ctx._clip_path.length > 0) {
                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");
                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(null, true);
                  this.ctx._clip_path = this.path;
                  this.path = origPath;
              }

              this.pdf.text(text, x, this._getBaseline(y), {
                  stroke: true
              }, degs);

              if (this.ctx._clip_path.length > 0) {
                  lines.push('Q');
              }
          },

          setFont: function setFont(font) {
              this.ctx.font = font;

              //var rx = /\s*(\w+)\s+(\w+)\s+(\w+)\s+([\d\.]+)(px|pt|em)\s+["']?(\w+)['"]?/;
              var rx = /\s*(\w+)\s+(\w+)\s+(\w+)\s+([\d\.]+)(px|pt|em)\s+(.*)?/;
              m = rx.exec(font);
              if (m != null) {
                  var fontStyle = m[1];
                  var fontVariant = m[2];
                  var fontWeight = m[3];
                  var fontSize = m[4];
                  var fontSizeUnit = m[5];
                  var fontFamily = m[6];

                  if ('px' === fontSizeUnit) {
                      fontSize = Math.floor(parseFloat(fontSize));
                      // fontSize = fontSize * 1.25;
                  } else if ('em' === fontSizeUnit) {
                      fontSize = Math.floor(parseFloat(fontSize) * this.pdf.getFontSize());
                  } else {
                      fontSize = Math.floor(parseFloat(fontSize));
                  }

                  this.pdf.setFontSize(fontSize);

                  if (fontWeight === 'bold' || fontWeight === '700') {
                      this.pdf.setFontStyle('bold');
                  } else {
                      if (fontStyle === 'italic') {
                          this.pdf.setFontStyle('italic');
                      } else {
                          this.pdf.setFontStyle('normal');
                      }
                  }

                  var name = fontFamily;
                  var parts = name.toLowerCase().split(/\s*,\s*/);
                  var jsPdfFontName;

                  if (parts.indexOf('arial') != -1) {
                      jsPdfFontName = 'Arial';
                  } else if (parts.indexOf('verdana') != -1) {
                      jsPdfFontName = 'Verdana';
                  } else if (parts.indexOf('helvetica') != -1) {
                      jsPdfFontName = 'Helvetica';
                  } else if (parts.indexOf('sans-serif') != -1) {
                      jsPdfFontName = 'sans-serif';
                  } else if (parts.indexOf('fixed') != -1) {
                      jsPdfFontName = 'Fixed';
                  } else if (parts.indexOf('monospace') != -1) {
                      jsPdfFontName = 'Monospace';
                  } else if (parts.indexOf('terminal') != -1) {
                      jsPdfFontName = 'Terminal';
                  } else if (parts.indexOf('courier') != -1) {
                      jsPdfFontName = 'Courier';
                  } else if (parts.indexOf('times') != -1) {
                      jsPdfFontName = 'Times';
                  } else if (parts.indexOf('cursive') != -1) {
                      jsPdfFontName = 'Cursive';
                  } else if (parts.indexOf('fantasy') != -1) {
                      jsPdfFontName = 'Fantasy';
                  } else if (parts.indexOf('serif') != -1) {
                      jsPdfFontName = 'Serif';
                  } else {
                      jsPdfFontName = 'Serif';
                  }

                  //TODO check more cases
                  var style;
                  if ('bold' === fontWeight) {
                      style = 'bold';
                  } else {
                      style = 'normal';
                  }

                  this.pdf.setFont(jsPdfFontName, style);
              } else {
                  var rx = /(\d+)(pt|px|em)\s+(\w+)\s*(\w+)?/;
                  var m = rx.exec(font);
                  if (m != null) {
                      var size = m[1];
                      var unit = m[2];
                      var name = m[3];
                      var style = m[4];
                      if (!style) {
                          style = 'normal';
                      }
                      if ('em' === fontSizeUnit) {
                          size = Math.floor(parseFloat(fontSize) * this.pdf.getFontSize());
                      } else {
                          size = Math.floor(parseFloat(size));
                      }
                      this.pdf.setFontSize(size);
                      this.pdf.setFont(name, style);
                  }
              }
          },

          setTextBaseline: function setTextBaseline(baseline) {
              this.ctx.textBaseline = baseline;
          },

          getTextBaseline: function getTextBaseline() {
              return this.ctx.textBaseline;
          },

          //TODO implement textAlign
          setTextAlign: function setTextAlign(align) {
              this.ctx.textAlign = align;
          },

          getTextAlign: function getTextAlign() {
              return this.ctx.textAlign;
          },

          setLineWidth: function setLineWidth(width) {
              this.ctx.lineWidth = width;
              this.pdf.setLineWidth(width);
          },

          setLineCap: function setLineCap(style) {
              this.ctx.lineCap = style;
              this.pdf.setLineCap(style);
          },

          setLineJoin: function setLineJoin(style) {
              this.ctx.lineJoin = style;
              this.pdf.setLineJoin(style);
          },

          moveTo: function moveTo(x, y) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'mt',
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          _wrapX: function _wrapX(x) {
              if (this.pageWrapXEnabled) {
                  return x % this.pageWrapX;
              } else {
                  return x;
              }
          },

          _wrapY: function _wrapY(y) {
              if (this.pageWrapYEnabled) {
                  this._gotoPage(this._page(y));
                  return (y - this.lastBreak) % this.pageWrapY;
              } else {
                  return y;
              }
          },

          transform: function transform(a, b, c, d, e, f) {
              //TODO apply to current transformation instead of replacing
              this.ctx._transform = [a, b, c, d, e, f];
          },

          setTransform: function setTransform(a, b, c, d, e, f) {
              this.ctx._transform = [a, b, c, d, e, f];
          },

          _getTransform: function _getTransform() {
              return this.ctx._transform;
          },

          lastBreak: 0,
          // Y Position of page breaks.
          pageBreaks: [],
          // returns: One-based Page Number
          // Should only be used if pageWrapYEnabled is true
          _page: function _page(y) {
              if (this.pageWrapYEnabled) {
                  this.lastBreak = 0;
                  var manualBreaks = 0;
                  var autoBreaks = 0;
                  for (var i = 0; i < this.pageBreaks.length; i++) {
                      if (y >= this.pageBreaks[i]) {
                          manualBreaks++;
                          if (this.lastBreak === 0) {
                              autoBreaks++;
                          }
                          var spaceBetweenLastBreak = this.pageBreaks[i] - this.lastBreak;
                          this.lastBreak = this.pageBreaks[i];
                          var pagesSinceLastBreak = Math.floor(spaceBetweenLastBreak / this.pageWrapY);
                          autoBreaks += pagesSinceLastBreak;
                      }
                  }
                  if (this.lastBreak === 0) {
                      var pagesSinceLastBreak = Math.floor(y / this.pageWrapY) + 1;
                      autoBreaks += pagesSinceLastBreak;
                  }
                  return autoBreaks + manualBreaks;
              } else {
                  return this.pdf.internal.getCurrentPageInfo().pageNumber;
              }
          },

          _gotoPage: function _gotoPage(pageOneBased) {
              // This is a stub to be overriden if needed
          },

          lineTo: function lineTo(x, y) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'lt',
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          bezierCurveTo: function bezierCurveTo(x1, y1, x2, y2, x, y) {
              x1 = this._wrapX(x1);
              y1 = this._wrapY(y1);
              x2 = this._wrapX(x2);
              y2 = this._wrapY(y2);
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt;
              xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x1, y1]);
              x1 = xpt[0];
              y1 = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x2, y2]);
              x2 = xpt[0];
              y2 = xpt[1];

              var obj = {
                  type: 'bct',
                  x1: x1,
                  y1: y1,
                  x2: x2,
                  y2: y2,
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          quadraticCurveTo: function quadraticCurveTo(x1, y1, x, y) {
              x1 = this._wrapX(x1);
              y1 = this._wrapY(y1);
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt;
              xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];
              xpt = this._matrix_map_point(this.ctx._transform, [x1, y1]);
              x1 = xpt[0];
              y1 = xpt[1];

              var obj = {
                  type: 'qct',
                  x1: x1,
                  y1: y1,
                  x: x,
                  y: y
              };
              this.path.push(obj);
          },

          arc: function arc(x, y, radius, startAngle, endAngle, anticlockwise) {
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xpt = this._matrix_map_point(this.ctx._transform, [x, y]);
              x = xpt[0];
              y = xpt[1];

              var obj = {
                  type: 'arc',
                  x: x,
                  y: y,
                  radius: radius,
                  startAngle: startAngle,
                  endAngle: endAngle,
                  anticlockwise: anticlockwise
              };
              this.path.push(obj);
          },

          drawImage: function drawImage(img, x, y, w, h, x2, y2, w2, h2) {
              if (x2 !== undefined) {
                  x = x2;
                  y = y2;
                  w = w2;
                  h = h2;
              }
              x = this._wrapX(x);
              y = this._wrapY(y);

              var xRect = this._matrix_map_rect(this.ctx._transform, { x: x, y: y, w: w, h: h });
              var xRect2 = this._matrix_map_rect(this.ctx._transform, { x: x2, y: y2, w: w2, h: h2 });

              // TODO implement source clipping and image scaling
              var format;
              var rx = /data:image\/(\w+).*/i;
              var m = rx.exec(img);
              if (m != null) {
                  format = m[1];
              } else {
                  // format = "jpeg";
                  format = "png";
              }

              this.pdf.addImage(img, format, xRect.x, xRect.y, xRect.w, xRect.h);
          },

          /**
           * Multiply the first matrix by the second
           * @param m1
           * @param m2
           * @returns {*[]}
           * @private
           */
          _matrix_multiply: function _matrix_multiply(m2, m1) {
              var sx = m1[0];
              var shy = m1[1];
              var shx = m1[2];
              var sy = m1[3];
              var tx = m1[4];
              var ty = m1[5];

              var t0 = sx * m2[0] + shy * m2[2];
              var t2 = shx * m2[0] + sy * m2[2];
              var t4 = tx * m2[0] + ty * m2[2] + m2[4];
              shy = sx * m2[1] + shy * m2[3];
              sy = shx * m2[1] + sy * m2[3];
              ty = tx * m2[1] + ty * m2[3] + m2[5];
              sx = t0;
              shx = t2;
              tx = t4;

              return [sx, shy, shx, sy, tx, ty];
          },

          _matrix_rotation: function _matrix_rotation(m) {
              return Math.atan2(m[2], m[0]);
          },

          _matrix_decompose: function _matrix_decompose(matrix) {

              var a = matrix[0];
              var b = matrix[1];
              var c = matrix[2];
              var d = matrix[3];

              var scaleX = Math.sqrt(a * a + b * b);
              a /= scaleX;
              b /= scaleX;

              var shear = a * c + b * d;
              c -= a * shear;
              d -= b * shear;

              var scaleY = Math.sqrt(c * c + d * d);
              c /= scaleY;
              d /= scaleY;
              shear /= scaleY;

              if (a * d < b * c) {
                  a = -a;
                  b = -b;
                  shear = -shear;
                  scaleX = -scaleX;
              }

              return {
                  scale: [scaleX, 0, 0, scaleY, 0, 0],
                  translate: [1, 0, 0, 1, matrix[4], matrix[5]],
                  rotate: [a, b, -b, a, 0, 0],
                  skew: [1, 0, shear, 1, 0, 0]
              };
          },

          _matrix_map_point: function _matrix_map_point(m1, pt) {
              var sx = m1[0];
              var shy = m1[1];
              var shx = m1[2];
              var sy = m1[3];
              var tx = m1[4];
              var ty = m1[5];

              var px = pt[0];
              var py = pt[1];

              var x = px * sx + py * shx + tx;
              var y = px * shy + py * sy + ty;
              return [x, y];
          },

          _matrix_map_point_obj: function _matrix_map_point_obj(m1, pt) {
              var xpt = this._matrix_map_point(m1, [pt.x, pt.y]);
              return { x: xpt[0], y: xpt[1] };
          },

          _matrix_map_rect: function _matrix_map_rect(m1, rect) {
              var p1 = this._matrix_map_point(m1, [rect.x, rect.y]);
              var p2 = this._matrix_map_point(m1, [rect.x + rect.w, rect.y + rect.h]);
              return { x: p1[0], y: p1[1], w: p2[0] - p1[0], h: p2[1] - p1[1] };
          },

          _matrix_is_identity: function _matrix_is_identity(m1) {
              if (m1[0] != 1) {
                  return false;
              }
              if (m1[1] != 0) {
                  return false;
              }
              if (m1[2] != 0) {
                  return false;
              }
              if (m1[3] != 1) {
                  return false;
              }
              if (m1[4] != 0) {
                  return false;
              }
              if (m1[5] != 0) {
                  return false;
              }
              return true;
          },

          rotate: function rotate(angle) {
              var matrix = [Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle), 0.0, 0.0];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          scale: function scale(sx, sy) {
              var matrix = [sx, 0.0, 0.0, sy, 0.0, 0.0];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          translate: function translate(x, y) {
              var matrix = [1.0, 0.0, 0.0, 1.0, x, y];
              this.ctx._transform = this._matrix_multiply(this.ctx._transform, matrix);
          },

          stroke: function stroke() {
              if (this.ctx._clip_path.length > 0) {

                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");

                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._stroke(true);

                  this.ctx._clip_path = this.path;
                  this.path = origPath;
                  this._stroke(false);

                  lines.push("Q");
              } else {
                  this._stroke(false);
              }
          },

          _stroke: function _stroke(isClip) {
              if (!isClip && this._isStrokeTransparent()) {
                  return;
              }

              //TODO opacity

              var moves = [];
              var closed = false;

              var xPath = this.path;

              for (var i = 0; i < xPath.length; i++) {
                  var pt = xPath[i];
                  switch (pt.type) {
                      case 'mt':
                          moves.push({ start: pt, deltas: [], abs: [] });
                          break;
                      case 'lt':
                          var delta = [pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'bct':
                          var delta = [pt.x1 - xPath[i - 1].x, pt.y1 - xPath[i - 1].y, pt.x2 - xPath[i - 1].x, pt.y2 - xPath[i - 1].y, pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'qct':
                          // convert to bezier
                          var x1 = xPath[i - 1].x + 2.0 / 3.0 * (pt.x1 - xPath[i - 1].x);
                          var y1 = xPath[i - 1].y + 2.0 / 3.0 * (pt.y1 - xPath[i - 1].y);
                          var x2 = pt.x + 2.0 / 3.0 * (pt.x1 - pt.x);
                          var y2 = pt.y + 2.0 / 3.0 * (pt.y1 - pt.y);
                          var x3 = pt.x;
                          var y3 = pt.y;
                          var delta = [x1 - xPath[i - 1].x, y1 - xPath[i - 1].y, x2 - xPath[i - 1].x, y2 - xPath[i - 1].y, x3 - xPath[i - 1].x, y3 - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'arc':
                          moves[moves.length - 1].arc = true;
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'close':
                          closed = true;
                          break;
                  }
              }

              for (var i = 0; i < moves.length; i++) {
                  var style;
                  if (i == moves.length - 1) {
                      style = 's';
                  } else {
                      style = null;
                  }
                  if (moves[i].arc) {
                      var arcs = moves[i].abs;
                      for (var ii = 0; ii < arcs.length; ii++) {
                          var arc = arcs[ii];
                          var start = arc.startAngle * 360 / (2 * Math.PI);
                          var end = arc.endAngle * 360 / (2 * Math.PI);
                          var x = arc.x;
                          var y = arc.y;
                          this.internal.arc2(this, x, y, arc.radius, start, end, arc.anticlockwise, style, isClip);
                      }
                  } else {
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      if (!isClip) {
                          this.pdf.lines(moves[i].deltas, x, y, null, style);
                      } else {
                          this.pdf.lines(moves[i].deltas, x, y, null, null);
                          this.pdf.clip_fixed();
                      }
                  }
              }
          },

          _isFillTransparent: function _isFillTransparent() {
              return this.ctx._isFillTransparent || this.globalAlpha == 0;
          },

          _isStrokeTransparent: function _isStrokeTransparent() {
              return this.ctx._isStrokeTransparent || this.globalAlpha == 0;
          },

          fill: function fill(fillRule) {
              //evenodd or nonzero (default)
              if (this.ctx._clip_path.length > 0) {

                  var lines;
                  if (window.outIntercept) {
                      lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
                  } else {
                      lines = this.pdf.internal.pages[1];
                  }
                  lines.push("q");

                  var origPath = this.path;
                  this.path = this.ctx._clip_path;
                  this.ctx._clip_path = [];
                  this._fill(fillRule, true);

                  this.ctx._clip_path = this.path;
                  this.path = origPath;
                  this._fill(fillRule, false);

                  lines.push('Q');
              } else {
                  this._fill(fillRule, false);
              }
          },

          _fill: function _fill(fillRule, isClip) {
              if (this._isFillTransparent()) {
                  return;
              }
              var v2Support = typeof this.pdf.internal.newObject2 === 'function';

              var lines;
              if (window.outIntercept) {
                  lines = window.outIntercept.type === 'group' ? window.outIntercept.stream : window.outIntercept;
              } else {
                  lines = this.pdf.internal.pages[1];
              }

              // if (this.ctx._clip_path.length > 0) {
              //     lines.push('q');
              //     var oldPath = this.path;
              //     this.path = this.ctx._clip_path;
              //     this.ctx._clip_path = [];
              //     this._fill(fillRule, true);
              //     this.ctx._clip_path = this.path;
              //     this.path = oldPath;
              // }

              var moves = [];
              var outInterceptOld = window.outIntercept;

              if (v2Support) {
                  // Blend and Mask
                  switch (this.ctx.globalCompositeOperation) {
                      case 'normal':
                      case 'source-over':
                          break;
                      case 'destination-in':
                      case 'destination-out':
                          //TODO this need to be added to the current group or page
                          // define a mask stream
                          var obj = this.pdf.internal.newStreamObject();

                          // define a mask state
                          var obj2 = this.pdf.internal.newObject2();
                          obj2.push('<</Type /ExtGState');
                          obj2.push('/SMask <</S /Alpha /G ' + obj.objId + ' 0 R>>'); // /S /Luminosity will need to define color space
                          obj2.push('>>');

                          // add mask to page resources
                          var gsName = 'MASK' + obj2.objId;
                          this.pdf.internal.addGraphicsState(gsName, obj2.objId);

                          var instruction = '/' + gsName + ' gs';
                          // add mask to page, group, or stream
                          lines.splice(0, 0, 'q');
                          lines.splice(1, 0, instruction);
                          lines.push('Q');

                          window.outIntercept = obj;
                          break;
                      default:
                          var dictionaryEntry = '/' + this.pdf.internal.blendModeMap[this.ctx.globalCompositeOperation.toUpperCase()];
                          if (dictionaryEntry) {
                              this.pdf.internal.out(dictionaryEntry + ' gs');
                          }
                          break;
                  }
              }

              var alpha = this.ctx.globalAlpha;
              if (this.ctx._fillOpacity < 1) {
                  // TODO combine this with global opacity
                  alpha = this.ctx._fillOpacity;
              }

              //TODO check for an opacity graphics state that was already created
              //TODO do not set opacity if current value is already active
              if (v2Support) {
                  var objOpac = this.pdf.internal.newObject2();
                  objOpac.push('<</Type /ExtGState');
                  //objOpac.push(this.ctx.globalAlpha + " CA"); // Stroke
                  //objOpac.push(this.ctx.globalAlpha + " ca"); // Not Stroke
                  objOpac.push('/CA ' + alpha); // Stroke
                  objOpac.push('/ca ' + alpha); // Not Stroke
                  objOpac.push('>>');
                  var gsName = 'GS_O_' + objOpac.objId;
                  this.pdf.internal.addGraphicsState(gsName, objOpac.objId);
                  this.pdf.internal.out('/' + gsName + ' gs');
              }

              var xPath = this.path;

              for (var i = 0; i < xPath.length; i++) {
                  var pt = xPath[i];
                  switch (pt.type) {
                      case 'mt':
                          moves.push({ start: pt, deltas: [], abs: [] });
                          break;
                      case 'lt':
                          var delta = [pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'bct':
                          var delta = [pt.x1 - xPath[i - 1].x, pt.y1 - xPath[i - 1].y, pt.x2 - xPath[i - 1].x, pt.y2 - xPath[i - 1].y, pt.x - xPath[i - 1].x, pt.y - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'qct':
                          // convert to bezier
                          var x1 = xPath[i - 1].x + 2.0 / 3.0 * (pt.x1 - xPath[i - 1].x);
                          var y1 = xPath[i - 1].y + 2.0 / 3.0 * (pt.y1 - xPath[i - 1].y);
                          var x2 = pt.x + 2.0 / 3.0 * (pt.x1 - pt.x);
                          var y2 = pt.y + 2.0 / 3.0 * (pt.y1 - pt.y);
                          var x3 = pt.x;
                          var y3 = pt.y;
                          var delta = [x1 - xPath[i - 1].x, y1 - xPath[i - 1].y, x2 - xPath[i - 1].x, y2 - xPath[i - 1].y, x3 - xPath[i - 1].x, y3 - xPath[i - 1].y];
                          moves[moves.length - 1].deltas.push(delta);
                          break;
                      case 'arc':
                          //TODO this was hack to avoid out of bounds issue
                          if (moves.length == 0) {
                              moves.push({ start: { x: 0, y: 0 }, deltas: [], abs: [] });
                          }
                          moves[moves.length - 1].arc = true;
                          moves[moves.length - 1].abs.push(pt);
                          break;
                      case 'close':
                          //moves[moves.length - 1].deltas.push('close');
                          break;
                  }
              }

              for (var i = 0; i < moves.length; i++) {
                  var style;
                  if (i == moves.length - 1) {
                      style = 'f';
                      if (fillRule === 'evenodd') {
                          style += '*';
                      }
                  } else {
                      style = null;
                  }

                  if (moves[i].arc) {
                      var arcs = moves[i].abs;
                      for (var ii = 0; ii < arcs.length; ii++) {
                          var arc = arcs[ii];
                          //TODO lines deltas were getting in here
                          if (typeof arc.startAngle !== 'undefined') {
                              var start = arc.startAngle * 360 / (2 * Math.PI);
                              var end = arc.endAngle * 360 / (2 * Math.PI);
                              // Add the current position (last move to)
                              //var x = moves[i].start.x + arc.x;
                              //var y = moves[i].start.y + arc.y;
                              var x = arc.x;
                              var y = arc.y;
                              if (ii == 0) {
                                  this.internal.move2(this, x, y);
                              }
                              this.internal.arc2(this, x, y, arc.radius, start, end, arc.anticlockwise, null, isClip);
                          } else {
                              this.internal.line2(c2d, arc.x, arc.y);
                          }
                      }

                      // extra move bug causing close to resolve to wrong point
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      this.internal.line2(c2d, x, y);

                      this.pdf.internal.out('h');
                      this.pdf.internal.out('f');
                  } else {
                      var x = moves[i].start.x;
                      var y = moves[i].start.y;
                      if (!isClip) {
                          this.pdf.lines(moves[i].deltas, x, y, null, style);
                      } else {
                          this.pdf.lines(moves[i].deltas, x, y, null, null);
                          this.pdf.clip_fixed();
                      }
                  }
              }

              window.outIntercept = outInterceptOld;

              // if (this.ctx._clip_path.length > 0) {
              //     lines.push('Q');
              // }
          },

          pushMask: function pushMask() {
              var v2Support = typeof this.pdf.internal.newObject2 === 'function';

              if (!v2Support) {
                  console.log('jsPDF v2 not enabled');
                  return;
              }

              // define a mask stream
              var obj = this.pdf.internal.newStreamObject();

              // define a mask state
              var obj2 = this.pdf.internal.newObject2();
              obj2.push('<</Type /ExtGState');
              obj2.push('/SMask <</S /Alpha /G ' + obj.objId + ' 0 R>>'); // /S /Luminosity will need to define color space
              obj2.push('>>');

              // add mask to page resources
              var gsName = 'MASK' + obj2.objId;
              this.pdf.internal.addGraphicsState(gsName, obj2.objId);

              var instruction = '/' + gsName + ' gs';
              this.pdf.internal.out(instruction);
          },

          clip: function clip() {
              //TODO do we reset the path, or just copy it?
              if (this.ctx._clip_path.length > 0) {
                  for (var i = 0; i < this.path.length; i++) {
                      this.ctx._clip_path.push(this.path[i]);
                  }
              } else {
                  this.ctx._clip_path = this.path;
              }
              this.path = [];
          },

          measureText: function measureText(text) {
              var pdf = this.pdf;
              return {
                  getWidth: function getWidth() {
                      var fontSize = pdf.internal.getFontSize();
                      var txtWidth = pdf.getStringUnitWidth(text) * fontSize / pdf.internal.scaleFactor;
                      return txtWidth;
                  },

                  get width() {
                      return this.getWidth(text);
                  }
              };
          },
          _getBaseline: function _getBaseline(y) {
              var height = parseInt(this.pdf.internal.getFontSize());
              // TODO Get descent from font descriptor
              var descent = height * .25;
              switch (this.ctx.textBaseline) {
                  case 'bottom':
                      return y - descent;
                  case 'top':
                      return y + height;
                  case 'hanging':
                      return y + height - descent;
                  case 'middle':
                      return y + height / 2 - descent;
                  case 'ideographic':
                      // TODO not implemented
                      return y;
                  case 'alphabetic':
                  default:
                      return y;
              }
          }
      };

      var c2d = jsPDFAPI.context2d;

      // accessor methods
      Object.defineProperty(c2d, 'fillStyle', {
          set: function set(value) {
              this.setFillStyle(value);
          },
          get: function get() {
              return this.ctx.fillStyle;
          }
      });
      Object.defineProperty(c2d, 'strokeStyle', {
          set: function set(value) {
              this.setStrokeStyle(value);
          },
          get: function get() {
              return this.ctx.strokeStyle;
          }
      });
      Object.defineProperty(c2d, 'lineWidth', {
          set: function set(value) {
              this.setLineWidth(value);
          },
          get: function get() {
              return this.ctx.lineWidth;
          }
      });
      Object.defineProperty(c2d, 'lineCap', {
          set: function set(val) {
              this.setLineCap(val);
          },
          get: function get() {
              return this.ctx.lineCap;
          }
      });
      Object.defineProperty(c2d, 'lineJoin', {
          set: function set(val) {
              this.setLineJoin(val);
          },
          get: function get() {
              return this.ctx.lineJoin;
          }
      });
      Object.defineProperty(c2d, 'miterLimit', {
          set: function set(val) {
              this.ctx.miterLimit = val;
          },
          get: function get() {
              return this.ctx.miterLimit;
          }
      });
      Object.defineProperty(c2d, 'textBaseline', {
          set: function set(value) {
              this.setTextBaseline(value);
          },
          get: function get() {
              return this.getTextBaseline();
          }
      });
      Object.defineProperty(c2d, 'textAlign', {
          set: function set(value) {
              this.setTextAlign(value);
          },
          get: function get() {
              return this.getTextAlign();
          }
      });
      Object.defineProperty(c2d, 'font', {
          set: function set(value) {
              this.setFont(value);
          },
          get: function get() {
              return this.ctx.font;
          }
      });
      Object.defineProperty(c2d, 'globalCompositeOperation', {
          set: function set(value) {
              this.ctx.globalCompositeOperation = value;
          },
          get: function get() {
              return this.ctx.globalCompositeOperation;
          }
      });
      Object.defineProperty(c2d, 'globalAlpha', {
          set: function set(value) {
              this.ctx.globalAlpha = value;
          },
          get: function get() {
              return this.ctx.globalAlpha;
          }
      });

      c2d.internal = {};

      c2d.internal.rxRgb = /rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/;
      c2d.internal.rxRgba = /rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d\.]+)\s*\)/;
      c2d.internal.rxTransparent = /transparent|rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*0+\s*\)/;

      // http://hansmuller-flex.blogspot.com/2011/10/more-about-approximating-circular-arcs.html
      c2d.internal.arc = function (c2d, xc, yc, r, a1, a2, anticlockwise, style) {
          var includeMove = true;

          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          var a1r = a1 * (Math.PI / 180);
          var a2r = a2 * (Math.PI / 180);
          var curves = this.createArc(r, a1r, a2r, anticlockwise);
          var pathData = null;

          for (var i = 0; i < curves.length; i++) {
              var curve = curves[i];
              if (includeMove && i == 0) {
                  this.pdf.internal.out([f2((curve.x1 + xc) * k), f2((pageHeight - (curve.y1 + yc)) * k), 'm', f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'].join(' '));
              } else {
                  this.pdf.internal.out([f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'].join(' '));
              }

              //c2d._lastPoint = {x: curve.x1 + xc, y: curve.y1 + yc};
              c2d._lastPoint = { x: xc, y: yc };
              // f2((curve.x1 + xc) * k), f2((pageHeight - (curve.y1 + yc)) * k), 'm', f2((curve.x2 + xc) * k), f2((pageHeight - (curve.y2 + yc)) * k), f2((curve.x3 + xc) * k), f2((pageHeight - (curve.y3 + yc)) * k), f2((curve.x4 + xc) * k), f2((pageHeight - (curve.y4 + yc)) * k), 'c'
          }

          if (style !== null) {
              this.pdf.internal.out(this.pdf.internal.getStyle(style));
          }
      };

      /**
       *
       * @param x Edge point X
       * @param y Edge point Y
       * @param r Radius
       * @param a1 start angle
       * @param a2 end angle
       * @param anticlockwise
       * @param style
       * @param isClip
       */
      c2d.internal.arc2 = function (c2d, x, y, r, a1, a2, anticlockwise, style, isClip) {
          // we need to convert from cartesian to polar here methinks.
          var centerX = x; // + r;
          var centerY = y;

          if (false) {}

          if (!isClip) {
              this.arc(c2d, centerX, centerY, r, a1, a2, anticlockwise, style);
          } else {
              this.arc(c2d, centerX, centerY, r, a1, a2, anticlockwise, null);
              this.pdf.clip_fixed();
          }
      };

      c2d.internal.move2 = function (c2d, x, y) {
          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          this.pdf.internal.out([f2(x * k), f2((pageHeight - y) * k), 'm'].join(' '));
          c2d._lastPoint = { x: x, y: y };
      };

      c2d.internal.line2 = function (c2d, dx, dy) {
          var k = this.pdf.internal.scaleFactor;
          var pageHeight = this.pdf.internal.pageSize.height;
          var f2 = this.pdf.internal.f2;

          //var pt = {x: c2d._lastPoint.x + dx, y: c2d._lastPoint.y + dy};
          var pt = { x: dx, y: dy };

          this.pdf.internal.out([f2(pt.x * k), f2((pageHeight - pt.y) * k), 'l'].join(' '));
          //this.pdf.internal.out('f');
          c2d._lastPoint = pt;
      };

      /**
       * Return a array of objects that represent bezier curves which approximate the circular arc centered at the origin, from startAngle to endAngle (radians) with the specified radius.
       *
       * Each bezier curve is an object with four points, where x1,y1 and x4,y4 are the arc's end points and x2,y2 and x3,y3 are the cubic bezier's control points.
       */

      c2d.internal.createArc = function (radius, startAngle, endAngle, anticlockwise) {

          var EPSILON = 0.00001; // Roughly 1/1000th of a degree, see below

          // normalize startAngle, endAngle to [-2PI, 2PI]
          var twoPI = Math.PI * 2;
          var startAngleN = startAngle;
          if (startAngleN < twoPI || startAngleN > twoPI) {
              startAngleN = startAngleN % twoPI;
          }
          var endAngleN = endAngle;
          if (endAngleN < twoPI || endAngleN > twoPI) {
              endAngleN = endAngleN % twoPI;
          }

          // Compute the sequence of arc curves, up to PI/2 at a time.
          // Total arc angle is less than 2PI.
          var curves = [];
          var piOverTwo = Math.PI / 2.0;
          // var sgn = (startAngle < endAngle) ? +1 : -1; // clockwise or counterclockwise
          var sgn = anticlockwise ? -1 : +1;

          var a1 = startAngle;
          for (var totalAngle = Math.min(twoPI, Math.abs(endAngleN - startAngleN)); totalAngle > EPSILON;) {
              var a2 = a1 + sgn * Math.min(totalAngle, piOverTwo);
              curves.push(this.createSmallArc(radius, a1, a2));
              totalAngle -= Math.abs(a2 - a1);
              a1 = a2;
          }

          return curves;
      };

      /**
       * Cubic bezier approximation of a circular arc centered at the origin, from (radians) a1 to a2, where a2-a1 < pi/2. The arc's radius is r.
       *
       * Returns an object with four points, where x1,y1 and x4,y4 are the arc's end points and x2,y2 and x3,y3 are the cubic bezier's control points.
       *
       * This algorithm is based on the approach described in: A. Riškus, "Approximation of a Cubic Bezier Curve by Circular Arcs and Vice Versa," Information Technology and Control, 35(4), 2006 pp. 371-378.
       */

      c2d.internal.createSmallArc = function (r, a1, a2) {
          // Compute all four points for an arc that subtends the same total angle
          // but is centered on the X-axis

          var a = (a2 - a1) / 2.0;

          var x4 = r * Math.cos(a);
          var y4 = r * Math.sin(a);
          var x1 = x4;
          var y1 = -y4;

          var q1 = x1 * x1 + y1 * y1;
          var q2 = q1 + x1 * x4 + y1 * y4;
          var k2 = 4 / 3 * (Math.sqrt(2 * q1 * q2) - q2) / (x1 * y4 - y1 * x4);

          var x2 = x1 - k2 * y1;
          var y2 = y1 + k2 * x1;
          var x3 = x2;
          var y3 = -y2;

          // Find the arc points' actual locations by computing x1,y1 and x4,y4
          // and rotating the control points by a + a1

          var ar = a + a1;
          var cos_ar = Math.cos(ar);
          var sin_ar = Math.sin(ar);

          return {
              x1: r * Math.cos(a1),
              y1: r * Math.sin(a1),
              x2: x2 * cos_ar - y2 * sin_ar,
              y2: x2 * sin_ar + y2 * cos_ar,
              x3: x3 * cos_ar - y3 * sin_ar,
              y3: x3 * sin_ar + y3 * cos_ar,
              x4: r * Math.cos(a2),
              y4: r * Math.sin(a2)
          };
      };

      function context() {
          this._isStrokeTransparent = false;
          this._strokeOpacity = 1;
          this.strokeStyle = '#000000';
          this.fillStyle = '#000000';
          this._isFillTransparent = false;
          this._fillOpacity = 1;
          this.font = "12pt times";
          this.textBaseline = 'alphabetic'; // top,bottom,middle,ideographic,alphabetic,hanging
          this.textAlign = 'start';
          this.lineWidth = 1;
          this.lineJoin = 'miter'; // round, bevel, miter
          this.lineCap = 'butt'; // butt, round, square
          this._transform = [1, 0, 0, 1, 0, 0]; // sx, shy, shx, sy, tx, ty
          this.globalCompositeOperation = 'normal';
          this.globalAlpha = 1.0;
          this._clip_path = [];
          // TODO miter limit //default 10

          this.copy = function (ctx) {
              this._isStrokeTransparent = ctx._isStrokeTransparent;
              this._strokeOpacity = ctx._strokeOpacity;
              this.strokeStyle = ctx.strokeStyle;
              this._isFillTransparent = ctx._isFillTransparent;
              this._fillOpacity = ctx._fillOpacity;
              this.fillStyle = ctx.fillStyle;
              this.font = ctx.font;
              this.lineWidth = ctx.lineWidth;
              this.lineJoin = ctx.lineJoin;
              this.lineCap = ctx.lineCap;
              this.textBaseline = ctx.textBaseline;
              this.textAlign = ctx.textAlign;
              this._fontSize = ctx._fontSize;
              this._transform = ctx._transform.slice(0);
              this.globalCompositeOperation = ctx.globalCompositeOperation;
              this.globalAlpha = ctx.globalAlpha;
              this._clip_path = ctx._clip_path.slice(0); //TODO deep copy?
          };
      }

      return this;
  })(jsPDF.API);

  /** @preserve
   * jsPDF fromHTML plugin. BETA stage. API subject to change. Needs browser
   * Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
   *               2014 Juan Pablo Gaviria, https://github.com/juanpgaviria
   *               2014 Diego Casorran, https://github.com/diegocr
   *               2014 Daniel Husar, https://github.com/danielhusar
   *               2014 Wolfgang Gassler, https://github.com/woolfg
   *               2014 Steven Spungin, https://github.com/flamenco
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
  	var clone, _DrillForContent, FontNameDB, FontStyleMap, TextAlignMap, FontWeightMap, FloatMap, ClearMap, GetCSS, PurgeWhiteSpace, Renderer, ResolveFont, ResolveUnitedNumber, UnitedNumberMap, elementHandledElsewhere, images, loadImgs, checkForFooter, process, tableToJson;
  	clone = function () {
  		return function (obj) {
  			Clone.prototype = obj;
  			return new Clone();
  		};
  		function Clone() {}
  	}();
  	PurgeWhiteSpace = function PurgeWhiteSpace(array) {
  		var fragment, i, l, lTrimmed, r, rTrimmed, trailingSpace;
  		i = 0;
  		l = array.length;
  		fragment = void 0;
  		lTrimmed = false;
  		rTrimmed = false;
  		while (!lTrimmed && i !== l) {
  			fragment = array[i] = array[i].trimLeft();
  			if (fragment) {
  				lTrimmed = true;
  			}
  			i++;
  		}
  		i = l - 1;
  		while (l && !rTrimmed && i !== -1) {
  			fragment = array[i] = array[i].trimRight();
  			if (fragment) {
  				rTrimmed = true;
  			}
  			i--;
  		}
  		r = /\s+$/g;
  		trailingSpace = true;
  		i = 0;
  		while (i !== l) {
  			// Leave the line breaks intact
  			if (array[i] != "\u2028") {
  				fragment = array[i].replace(/\s+/g, " ");
  				if (trailingSpace) {
  					fragment = fragment.trimLeft();
  				}
  				if (fragment) {
  					trailingSpace = r.test(fragment);
  				}
  				array[i] = fragment;
  			}
  			i++;
  		}
  		return array;
  	};
  	Renderer = function Renderer(pdf, x, y, settings) {
  		this.pdf = pdf;
  		this.x = x;
  		this.y = y;
  		this.settings = settings;
  		//list of functions which are called after each element-rendering process
  		this.watchFunctions = [];
  		this.init();
  		return this;
  	};
  	ResolveFont = function ResolveFont(css_font_family_string) {
  		var name, part, parts;
  		name = void 0;
  		parts = css_font_family_string.split(",");
  		part = parts.shift();
  		while (!name && part) {
  			name = FontNameDB[part.trim().toLowerCase()];
  			part = parts.shift();
  		}
  		return name;
  	};
  	ResolveUnitedNumber = function ResolveUnitedNumber(css_line_height_string) {

  		//IE8 issues
  		css_line_height_string = css_line_height_string === "auto" ? "0px" : css_line_height_string;
  		if (css_line_height_string.indexOf("em") > -1 && !isNaN(Number(css_line_height_string.replace("em", "")))) {
  			css_line_height_string = Number(css_line_height_string.replace("em", "")) * 18.719 + "px";
  		}
  		if (css_line_height_string.indexOf("pt") > -1 && !isNaN(Number(css_line_height_string.replace("pt", "")))) {
  			css_line_height_string = Number(css_line_height_string.replace("pt", "")) * 1.333 + "px";
  		}

  		var normal, undef, value;
  		undef = void 0;
  		normal = 16.00;
  		value = UnitedNumberMap[css_line_height_string];
  		if (value) {
  			return value;
  		}
  		value = {
  			"xx-small": 9,
  			"x-small": 11,
  			small: 13,
  			medium: 16,
  			large: 19,
  			"x-large": 23,
  			"xx-large": 28,
  			auto: 0
  		}[{ css_line_height_string: css_line_height_string }];

  		if (value !== undef) {
  			return UnitedNumberMap[css_line_height_string] = value / normal;
  		}
  		if (value = parseFloat(css_line_height_string)) {
  			return UnitedNumberMap[css_line_height_string] = value / normal;
  		}
  		value = css_line_height_string.match(/([\d\.]+)(px)/);
  		if (value.length === 3) {
  			return UnitedNumberMap[css_line_height_string] = parseFloat(value[1]) / normal;
  		}
  		return UnitedNumberMap[css_line_height_string] = 1;
  	};
  	GetCSS = function GetCSS(element) {
  		var css, tmp, computedCSSElement;
  		computedCSSElement = function (el) {
  			var compCSS;
  			compCSS = function (el) {
  				if (document.defaultView && document.defaultView.getComputedStyle) {
  					return document.defaultView.getComputedStyle(el, null);
  				} else if (el.currentStyle) {
  					return el.currentStyle;
  				} else {
  					return el.style;
  				}
  			}(el);
  			return function (prop) {
  				prop = prop.replace(/-\D/g, function (match) {
  					return match.charAt(1).toUpperCase();
  				});
  				return compCSS[prop];
  			};
  		}(element);
  		css = {};
  		tmp = void 0;
  		css["font-family"] = ResolveFont(computedCSSElement("font-family")) || "times";
  		css["font-style"] = FontStyleMap[computedCSSElement("font-style")] || "normal";
  		css["text-align"] = TextAlignMap[computedCSSElement("text-align")] || "left";
  		tmp = FontWeightMap[computedCSSElement("font-weight")] || "normal";
  		if (tmp === "bold") {
  			if (css["font-style"] === "normal") {
  				css["font-style"] = tmp;
  			} else {
  				css["font-style"] = tmp + css["font-style"];
  			}
  		}
  		css["font-size"] = ResolveUnitedNumber(computedCSSElement("font-size")) || 1;
  		css["line-height"] = ResolveUnitedNumber(computedCSSElement("line-height")) || 1;
  		css["display"] = computedCSSElement("display") === "inline" ? "inline" : "block";

  		tmp = css["display"] === "block";
  		css["margin-top"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-top")) || 0;
  		css["margin-bottom"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-bottom")) || 0;
  		css["padding-top"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-top")) || 0;
  		css["padding-bottom"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-bottom")) || 0;
  		css["margin-left"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-left")) || 0;
  		css["margin-right"] = tmp && ResolveUnitedNumber(computedCSSElement("margin-right")) || 0;
  		css["padding-left"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-left")) || 0;
  		css["padding-right"] = tmp && ResolveUnitedNumber(computedCSSElement("padding-right")) || 0;

  		css["page-break-before"] = computedCSSElement("page-break-before") || "auto";

  		//float and clearing of floats
  		css["float"] = FloatMap[computedCSSElement("cssFloat")] || "none";
  		css["clear"] = ClearMap[computedCSSElement("clear")] || "none";

  		css["color"] = computedCSSElement("color");

  		return css;
  	};
  	elementHandledElsewhere = function elementHandledElsewhere(element, renderer, elementHandlers) {
  		var handlers, i, isHandledElsewhere, l, t;
  		isHandledElsewhere = false;
  		i = void 0;
  		l = void 0;
  		t = void 0;
  		handlers = elementHandlers["#" + element.id];
  		if (handlers) {
  			if (typeof handlers === "function") {
  				isHandledElsewhere = handlers(element, renderer);
  			} else {
  				i = 0;
  				l = handlers.length;
  				while (!isHandledElsewhere && i !== l) {
  					isHandledElsewhere = handlers[i](element, renderer);
  					i++;
  				}
  			}
  		}
  		handlers = elementHandlers[element.nodeName];
  		if (!isHandledElsewhere && handlers) {
  			if (typeof handlers === "function") {
  				isHandledElsewhere = handlers(element, renderer);
  			} else {
  				i = 0;
  				l = handlers.length;
  				while (!isHandledElsewhere && i !== l) {
  					isHandledElsewhere = handlers[i](element, renderer);
  					i++;
  				}
  			}
  		}
  		return isHandledElsewhere;
  	};
  	tableToJson = function tableToJson(table, renderer) {
  		var data, headers, i, j, rowData, tableRow, table_obj, table_with, cell, l;
  		data = [];
  		headers = [];
  		i = 0;
  		l = table.rows[0].cells.length;
  		table_with = table.clientWidth;
  		while (i < l) {
  			cell = table.rows[0].cells[i];
  			headers[i] = {
  				name: cell.textContent.toLowerCase().replace(/\s+/g, ''),
  				prompt: cell.textContent.replace(/\r?\n/g, ''),
  				width: cell.clientWidth / table_with * renderer.pdf.internal.pageSize.width
  			};
  			i++;
  		}
  		i = 1;
  		while (i < table.rows.length) {
  			tableRow = table.rows[i];
  			rowData = {};
  			j = 0;
  			while (j < tableRow.cells.length) {
  				rowData[headers[j].name] = tableRow.cells[j].textContent.replace(/\r?\n/g, '');
  				j++;
  			}
  			data.push(rowData);
  			i++;
  		}
  		return table_obj = {
  			rows: data,
  			headers: headers
  		};
  	};
  	var SkipNode = {
  		SCRIPT: 1,
  		STYLE: 1,
  		NOSCRIPT: 1,
  		OBJECT: 1,
  		EMBED: 1,
  		SELECT: 1
  	};
  	var listCount = 1;
  	_DrillForContent = function DrillForContent(element, renderer, elementHandlers) {
  		var cn, cns, fragmentCSS, i, isBlock, l, px2pt, table2json, cb;
  		cns = element.childNodes;
  		cn = void 0;
  		fragmentCSS = GetCSS(element);
  		isBlock = fragmentCSS.display === "block";
  		if (isBlock) {
  			renderer.setBlockBoundary();
  			renderer.setBlockStyle(fragmentCSS);
  		}
  		px2pt = 0.264583 * 72 / 25.4;
  		i = 0;
  		l = cns.length;
  		while (i < l) {
  			cn = cns[i];
  			if ((typeof cn === "undefined" ? "undefined" : _typeof(cn)) === "object") {

  				//execute all watcher functions to e.g. reset floating
  				renderer.executeWatchFunctions(cn);

  				/*** HEADER rendering **/
  				if (cn.nodeType === 1 && cn.nodeName === 'HEADER') {
  					var header = cn;
  					//store old top margin
  					var oldMarginTop = renderer.pdf.margins_doc.top;
  					//subscribe for new page event and render header first on every page
  					renderer.pdf.internal.events.subscribe('addPage', function (pageInfo) {
  						//set current y position to old margin
  						renderer.y = oldMarginTop;
  						//render all child nodes of the header element
  						_DrillForContent(header, renderer, elementHandlers);
  						//set margin to old margin + rendered header + 10 space to prevent overlapping
  						//important for other plugins (e.g. table) to start rendering at correct position after header
  						renderer.pdf.margins_doc.top = renderer.y + 10;
  						renderer.y += 10;
  					}, false);
  				}

  				if (cn.nodeType === 8 && cn.nodeName === "#comment") {
  					if (~cn.textContent.indexOf("ADD_PAGE")) {
  						renderer.pdf.addPage();
  						renderer.y = renderer.pdf.margins_doc.top;
  					}
  				} else if (cn.nodeType === 1 && !SkipNode[cn.nodeName]) {
  					/*** IMAGE RENDERING ***/
  					var cached_image;
  					if (cn.nodeName === "IMG") {
  						var url = cn.getAttribute("src");
  						cached_image = images[renderer.pdf.sHashCode(url) || url];
  					}
  					if (cached_image) {
  						if (renderer.pdf.internal.pageSize.height - renderer.pdf.margins_doc.bottom < renderer.y + cn.height && renderer.y > renderer.pdf.margins_doc.top) {
  							renderer.pdf.addPage();
  							renderer.y = renderer.pdf.margins_doc.top;
  							//check if we have to set back some values due to e.g. header rendering for new page
  							renderer.executeWatchFunctions(cn);
  						}

  						var imagesCSS = GetCSS(cn);
  						var imageX = renderer.x;
  						var fontToUnitRatio = 12 / renderer.pdf.internal.scaleFactor;

  						//define additional paddings, margins which have to be taken into account for margin calculations
  						var additionalSpaceLeft = (imagesCSS["margin-left"] + imagesCSS["padding-left"]) * fontToUnitRatio;
  						var additionalSpaceRight = (imagesCSS["margin-right"] + imagesCSS["padding-right"]) * fontToUnitRatio;
  						var additionalSpaceTop = (imagesCSS["margin-top"] + imagesCSS["padding-top"]) * fontToUnitRatio;
  						var additionalSpaceBottom = (imagesCSS["margin-bottom"] + imagesCSS["padding-bottom"]) * fontToUnitRatio;

  						//if float is set to right, move the image to the right border
  						//add space if margin is set
  						if (imagesCSS['float'] !== undefined && imagesCSS['float'] === 'right') {
  							imageX += renderer.settings.width - cn.width - additionalSpaceRight;
  						} else {
  							imageX += additionalSpaceLeft;
  						}

  						renderer.pdf.addImage(cached_image, imageX, renderer.y + additionalSpaceTop, cn.width, cn.height);
  						cached_image = undefined;
  						//if the float prop is specified we have to float the text around the image
  						if (imagesCSS['float'] === 'right' || imagesCSS['float'] === 'left') {
  							//add functiont to set back coordinates after image rendering
  							renderer.watchFunctions.push(function (diffX, thresholdY, diffWidth, el) {
  								//undo drawing box adaptions which were set by floating
  								if (renderer.y >= thresholdY) {
  									renderer.x += diffX;
  									renderer.settings.width += diffWidth;
  									return true;
  								} else if (el && el.nodeType === 1 && !SkipNode[el.nodeName] && renderer.x + el.width > renderer.pdf.margins_doc.left + renderer.pdf.margins_doc.width) {
  									renderer.x += diffX;
  									renderer.y = thresholdY;
  									renderer.settings.width += diffWidth;
  									return true;
  								} else {
  									return false;
  								}
  							}.bind(this, imagesCSS['float'] === 'left' ? -cn.width - additionalSpaceLeft - additionalSpaceRight : 0, renderer.y + cn.height + additionalSpaceTop + additionalSpaceBottom, cn.width));
  							//reset floating by clear:both divs
  							//just set cursorY after the floating element
  							renderer.watchFunctions.push(function (yPositionAfterFloating, pages, el) {
  								if (renderer.y < yPositionAfterFloating && pages === renderer.pdf.internal.getNumberOfPages()) {
  									if (el.nodeType === 1 && GetCSS(el).clear === 'both') {
  										renderer.y = yPositionAfterFloating;
  										return true;
  									} else {
  										return false;
  									}
  								} else {
  									return true;
  								}
  							}.bind(this, renderer.y + cn.height, renderer.pdf.internal.getNumberOfPages()));

  							//if floating is set we decrease the available width by the image width
  							renderer.settings.width -= cn.width + additionalSpaceLeft + additionalSpaceRight;
  							//if left just add the image width to the X coordinate
  							if (imagesCSS['float'] === 'left') {
  								renderer.x += cn.width + additionalSpaceLeft + additionalSpaceRight;
  							}
  						} else {
  							//if no floating is set, move the rendering cursor after the image height
  							renderer.y += cn.height + additionalSpaceTop + additionalSpaceBottom;
  						}

  						/*** TABLE RENDERING ***/
  					} else if (cn.nodeName === "TABLE") {
  						table2json = tableToJson(cn, renderer);
  						renderer.y += 10;
  						renderer.pdf.table(renderer.x, renderer.y, table2json.rows, table2json.headers, {
  							autoSize: false,
  							printHeaders: elementHandlers.printHeaders,
  							margins: renderer.pdf.margins_doc,
  							css: GetCSS(cn)
  						});
  						renderer.y = renderer.pdf.lastCellPos.y + renderer.pdf.lastCellPos.h + 20;
  					} else if (cn.nodeName === "OL" || cn.nodeName === "UL") {
  						listCount = 1;
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  						renderer.y += 10;
  					} else if (cn.nodeName === "LI") {
  						var temp = renderer.x;
  						renderer.x += 20 / renderer.pdf.internal.scaleFactor;
  						renderer.y += 3;
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  						renderer.x = temp;
  					} else if (cn.nodeName === "BR") {
  						renderer.y += fragmentCSS["font-size"] * renderer.pdf.internal.scaleFactor;
  						renderer.addText("\u2028", clone(fragmentCSS));
  					} else {
  						if (!elementHandledElsewhere(cn, renderer, elementHandlers)) {
  							_DrillForContent(cn, renderer, elementHandlers);
  						}
  					}
  				} else if (cn.nodeType === 3) {
  					var value = cn.nodeValue;
  					if (cn.nodeValue && cn.parentNode.nodeName === "LI") {
  						if (cn.parentNode.parentNode.nodeName === "OL") {
  							value = listCount++ + '. ' + value;
  						} else {
  							var fontSize = fragmentCSS["font-size"];
  							var offsetX = (3 - fontSize * 0.75) * renderer.pdf.internal.scaleFactor;
  							var offsetY = fontSize * 0.75 * renderer.pdf.internal.scaleFactor;
  							var radius = fontSize * 1.74 / renderer.pdf.internal.scaleFactor;
  							cb = function cb(x, y) {
  								this.pdf.circle(x + offsetX, y + offsetY, radius, 'FD');
  							};
  						}
  					}
  					// Only add the text if the text node is in the body element
  					// Add compatibility with IE11
  					if (!!(cn.ownerDocument.body.compareDocumentPosition(cn) & 16)) {
  						renderer.addText(value, fragmentCSS);
  					}
  				} else if (typeof cn === "string") {
  					renderer.addText(cn, fragmentCSS);
  				}
  			}
  			i++;
  		}
  		elementHandlers.outY = renderer.y;

  		if (isBlock) {
  			return renderer.setBlockBoundary(cb);
  		}
  	};
  	images = {};
  	loadImgs = function loadImgs(element, renderer, elementHandlers, cb) {
  		var imgs = element.getElementsByTagName('img'),
  		    l = imgs.length,
  		    found_images,
  		    x = 0;
  		function done() {
  			renderer.pdf.internal.events.publish('imagesLoaded');
  			cb(found_images);
  		}
  		function loadImage(url, width, height) {
  			if (!url) return;
  			var img = new Image();
  			found_images = ++x;
  			img.crossOrigin = '';
  			img.onerror = img.onload = function () {
  				if (img.complete) {
  					//to support data urls in images, set width and height
  					//as those values are not recognized automatically
  					if (img.src.indexOf('data:image/') === 0) {
  						img.width = width || img.width || 0;
  						img.height = height || img.height || 0;
  					}
  					//if valid image add to known images array
  					if (img.width + img.height) {
  						var hash = renderer.pdf.sHashCode(url) || url;
  						images[hash] = images[hash] || img;
  					}
  				}
  				if (! --x) {
  					done();
  				}
  			};
  			img.src = url;
  		}
  		while (l--) {
  			loadImage(imgs[l].getAttribute("src"), imgs[l].width, imgs[l].height);
  		}return x || done();
  	};
  	checkForFooter = function checkForFooter(elem, renderer, elementHandlers) {
  		//check if we can found a <footer> element
  		var footer = elem.getElementsByTagName("footer");
  		if (footer.length > 0) {

  			footer = footer[0];

  			//bad hack to get height of footer
  			//creat dummy out and check new y after fake rendering
  			var oldOut = renderer.pdf.internal.write;
  			var oldY = renderer.y;
  			renderer.pdf.internal.write = function () {};
  			_DrillForContent(footer, renderer, elementHandlers);
  			var footerHeight = Math.ceil(renderer.y - oldY) + 5;
  			renderer.y = oldY;
  			renderer.pdf.internal.write = oldOut;

  			//add 20% to prevent overlapping
  			renderer.pdf.margins_doc.bottom += footerHeight;

  			//Create function render header on every page
  			var renderFooter = function renderFooter(pageInfo) {
  				var pageNumber = pageInfo !== undefined ? pageInfo.pageNumber : 1;
  				//set current y position to old margin
  				var oldPosition = renderer.y;
  				//render all child nodes of the header element
  				renderer.y = renderer.pdf.internal.pageSize.height - renderer.pdf.margins_doc.bottom;
  				renderer.pdf.margins_doc.bottom -= footerHeight;

  				//check if we have to add page numbers
  				var spans = footer.getElementsByTagName('span');
  				for (var i = 0; i < spans.length; ++i) {
  					//if we find some span element with class pageCounter, set the page
  					if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" pageCounter ") > -1) {
  						spans[i].innerHTML = pageNumber;
  					}
  					//if we find some span element with class totalPages, set a variable which is replaced after rendering of all pages
  					if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" totalPages ") > -1) {
  						spans[i].innerHTML = '###jsPDFVarTotalPages###';
  					}
  				}

  				//render footer content
  				_DrillForContent(footer, renderer, elementHandlers);
  				//set bottom margin to previous height including the footer height
  				renderer.pdf.margins_doc.bottom += footerHeight;
  				//important for other plugins (e.g. table) to start rendering at correct position after header
  				renderer.y = oldPosition;
  			};

  			//check if footer contains totalPages which should be replace at the disoposal of the document
  			var spans = footer.getElementsByTagName('span');
  			for (var i = 0; i < spans.length; ++i) {
  				if ((" " + spans[i].className + " ").replace(/[\n\t]/g, " ").indexOf(" totalPages ") > -1) {
  					renderer.pdf.internal.events.subscribe('htmlRenderingFinished', renderer.pdf.putTotalPages.bind(renderer.pdf, '###jsPDFVarTotalPages###'), true);
  				}
  			}

  			//register event to render footer on every new page
  			renderer.pdf.internal.events.subscribe('addPage', renderFooter, false);
  			//render footer on first page
  			renderFooter();

  			//prevent footer rendering
  			SkipNode['FOOTER'] = 1;
  		}
  	};
  	process = function process(pdf, element, x, y, settings, callback) {
  		if (!element) return false;
  		if (typeof element !== "string" && !element.parentNode) element = '' + element.innerHTML;
  		if (typeof element === "string") {
  			element = function (element) {
  				var $frame, $hiddendiv, framename, visuallyhidden;
  				framename = "jsPDFhtmlText" + Date.now().toString() + (Math.random() * 1000).toFixed(0);
  				visuallyhidden = "position: absolute !important;" + "clip: rect(1px 1px 1px 1px); /* IE6, IE7 */" + "clip: rect(1px, 1px, 1px, 1px);" + "padding:0 !important;" + "border:0 !important;" + "height: 1px !important;" + "width: 1px !important; " + "top:auto;" + "left:-100px;" + "overflow: hidden;";
  				$hiddendiv = document.createElement('div');
  				$hiddendiv.style.cssText = visuallyhidden;
  				$hiddendiv.innerHTML = "<iframe style=\"height:1px;width:1px\" name=\"" + framename + "\" />";
  				document.body.appendChild($hiddendiv);
  				$frame = window.frames[framename];
  				$frame.document.open();
  				$frame.document.writeln(element);
  				$frame.document.close();
  				return $frame.document.body;
  			}(element.replace(/<\/?script[^>]*?>/gi, ''));
  		}
  		var r = new Renderer(pdf, x, y, settings),
  		    out;

  		// 1. load images
  		// 2. prepare optional footer elements
  		// 3. render content
  		loadImgs.call(this, element, r, settings.elementHandlers, function (found_images) {
  			checkForFooter(element, r, settings.elementHandlers);
  			_DrillForContent(element, r, settings.elementHandlers);
  			//send event dispose for final taks (e.g. footer totalpage replacement)
  			r.pdf.internal.events.publish('htmlRenderingFinished');
  			out = r.dispose();
  			if (typeof callback === 'function') callback(out);else if (found_images) console.error('jsPDF Warning: rendering issues? provide a callback to fromHTML!');
  		});
  		return out || { x: r.x, y: r.y };
  	};
  	Renderer.prototype.init = function () {
  		this.paragraph = {
  			text: [],
  			style: []
  		};
  		return this.pdf.internal.write("q");
  	};
  	Renderer.prototype.dispose = function () {
  		this.pdf.internal.write("Q");
  		return {
  			x: this.x,
  			y: this.y,
  			ready: true
  		};
  	};

  	//Checks if we have to execute some watcher functions
  	//e.g. to end text floating around an image
  	Renderer.prototype.executeWatchFunctions = function (el) {
  		var ret = false;
  		var narray = [];
  		if (this.watchFunctions.length > 0) {
  			for (var i = 0; i < this.watchFunctions.length; ++i) {
  				if (this.watchFunctions[i](el) === true) {
  					ret = true;
  				} else {
  					narray.push(this.watchFunctions[i]);
  				}
  			}
  			this.watchFunctions = narray;
  		}
  		return ret;
  	};

  	Renderer.prototype.splitFragmentsIntoLines = function (fragments, styles) {
  		var currentLineLength, defaultFontSize, ff, fontMetrics, fontMetricsCache, fragment, fragmentChopped, fragmentLength, fragmentSpecificMetrics, fs, k, line, lines, maxLineLength, style;
  		defaultFontSize = 12;
  		k = this.pdf.internal.scaleFactor;
  		fontMetricsCache = {};
  		ff = void 0;
  		fs = void 0;
  		fontMetrics = void 0;
  		fragment = void 0;
  		style = void 0;
  		fragmentSpecificMetrics = void 0;
  		fragmentLength = void 0;
  		fragmentChopped = void 0;
  		line = [];
  		lines = [line];
  		currentLineLength = 0;
  		maxLineLength = this.settings.width;
  		while (fragments.length) {
  			fragment = fragments.shift();
  			style = styles.shift();
  			if (fragment) {
  				ff = style["font-family"];
  				fs = style["font-style"];
  				fontMetrics = fontMetricsCache[ff + fs];
  				if (!fontMetrics) {
  					fontMetrics = this.pdf.internal.getFont(ff, fs).metadata.Unicode;
  					fontMetricsCache[ff + fs] = fontMetrics;
  				}
  				fragmentSpecificMetrics = {
  					widths: fontMetrics.widths,
  					kerning: fontMetrics.kerning,
  					fontSize: style["font-size"] * defaultFontSize,
  					textIndent: currentLineLength
  				};
  				fragmentLength = this.pdf.getStringUnitWidth(fragment, fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				if (fragment == "\u2028") {
  					line = [];
  					lines.push(line);
  				} else if (currentLineLength + fragmentLength > maxLineLength) {
  					fragmentChopped = this.pdf.splitTextToSize(fragment, maxLineLength, fragmentSpecificMetrics);
  					line.push([fragmentChopped.shift(), style]);
  					while (fragmentChopped.length) {
  						line = [[fragmentChopped.shift(), style]];
  						lines.push(line);
  					}
  					currentLineLength = this.pdf.getStringUnitWidth(line[0][0], fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				} else {
  					line.push([fragment, style]);
  					currentLineLength += fragmentLength;
  				}
  			}
  		}

  		//if text alignment was set, set margin/indent of each line
  		if (style['text-align'] !== undefined && (style['text-align'] === 'center' || style['text-align'] === 'right' || style['text-align'] === 'justify')) {
  			for (var i = 0; i < lines.length; ++i) {
  				var length = this.pdf.getStringUnitWidth(lines[i][0][0], fragmentSpecificMetrics) * fragmentSpecificMetrics.fontSize / k;
  				//if there is more than on line we have to clone the style object as all lines hold a reference on this object
  				if (i > 0) {
  					lines[i][0][1] = clone(lines[i][0][1]);
  				}
  				var space = maxLineLength - length;

  				if (style['text-align'] === 'right') {
  					lines[i][0][1]['margin-left'] = space;
  					//if alignment is not right, it has to be center so split the space to the left and the right
  				} else if (style['text-align'] === 'center') {
  					lines[i][0][1]['margin-left'] = space / 2;
  					//if justify was set, calculate the word spacing and define in by using the css property
  				} else if (style['text-align'] === 'justify') {
  					var countSpaces = lines[i][0][0].split(' ').length - 1;
  					lines[i][0][1]['word-spacing'] = space / countSpaces;
  					//ignore the last line in justify mode
  					if (i === lines.length - 1) {
  						lines[i][0][1]['word-spacing'] = 0;
  					}
  				}
  			}
  		}

  		return lines;
  	};
  	Renderer.prototype.RenderTextFragment = function (text, style) {
  		var defaultFontSize, font, maxLineHeight;

  		maxLineHeight = 0;
  		defaultFontSize = 12;

  		if (this.pdf.internal.pageSize.height - this.pdf.margins_doc.bottom < this.y + this.pdf.internal.getFontSize()) {
  			this.pdf.internal.write("ET", "Q");
  			this.pdf.addPage();
  			this.y = this.pdf.margins_doc.top;
  			this.pdf.internal.write("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), style.color, "Td");
  			//move cursor by one line on new page
  			maxLineHeight = Math.max(maxLineHeight, style["line-height"], style["font-size"]);
  			this.pdf.internal.write(0, (-1 * defaultFontSize * maxLineHeight).toFixed(2), "Td");
  		}

  		font = this.pdf.internal.getFont(style["font-family"], style["font-style"]);

  		// text color
  		var pdfTextColor = this.getPdfColor(style["color"]);
  		if (pdfTextColor !== this.lastTextColor) {
  			this.pdf.internal.write(pdfTextColor);
  			this.lastTextColor = pdfTextColor;
  		}

  		//set the word spacing for e.g. justify style
  		if (style['word-spacing'] !== undefined && style['word-spacing'] > 0) {
  			this.pdf.internal.write(style['word-spacing'].toFixed(2), "Tw");
  		}

  		this.pdf.internal.write("/" + font.id, (defaultFontSize * style["font-size"]).toFixed(2), "Tf", "(" + this.pdf.internal.pdfEscape(text) + ") Tj");

  		//set the word spacing back to neutral => 0
  		if (style['word-spacing'] !== undefined) {
  			this.pdf.internal.write(0, "Tw");
  		}
  	};

  	// Accepts #FFFFFF, rgb(int,int,int), or CSS Color Name
  	Renderer.prototype.getPdfColor = function (style) {
  		var textColor;
  		var r, g, b;

  		var rx = /rgb\s*\(\s*(\d+),\s*(\d+),\s*(\d+\s*)\)/;
  		var m = rx.exec(style);
  		if (m != null) {
  			r = parseInt(m[1]);
  			g = parseInt(m[2]);
  			b = parseInt(m[3]);
  		} else {
  			if (style.charAt(0) != '#') {
  				style = CssColors.colorNameToHex(style);
  				if (!style) {
  					style = '#000000';
  				}
  			}
  			r = style.substring(1, 3);
  			r = parseInt(r, 16);
  			g = style.substring(3, 5);
  			g = parseInt(g, 16);
  			b = style.substring(5, 7);
  			b = parseInt(b, 16);
  		}

  		if (typeof r === 'string' && /^#[0-9A-Fa-f]{6}$/.test(r)) {
  			var hex = parseInt(r.substr(1), 16);
  			r = hex >> 16 & 255;
  			g = hex >> 8 & 255;
  			b = hex & 255;
  		}

  		var f3 = this.f3;
  		if (r === 0 && g === 0 && b === 0 || typeof g === 'undefined') {
  			textColor = f3(r / 255) + ' g';
  		} else {
  			textColor = [f3(r / 255), f3(g / 255), f3(b / 255), 'rg'].join(' ');
  		}
  		return textColor;
  	};

  	Renderer.prototype.f3 = function (number) {
  		return number.toFixed(3); // Ie, %.3f
  	}, Renderer.prototype.renderParagraph = function (cb) {
  		var blockstyle, defaultFontSize, fontToUnitRatio, fragments, i, l, line, lines, maxLineHeight, out, paragraphspacing_after, paragraphspacing_before, priorblockstyle, styles, fontSize;
  		fragments = PurgeWhiteSpace(this.paragraph.text);
  		styles = this.paragraph.style;
  		blockstyle = this.paragraph.blockstyle;
  		priorblockstyle = this.paragraph.priorblockstyle || {};
  		this.paragraph = {
  			text: [],
  			style: [],
  			blockstyle: {},
  			priorblockstyle: blockstyle
  		};
  		if (!fragments.join("").trim()) {
  			return;
  		}
  		lines = this.splitFragmentsIntoLines(fragments, styles);
  		line = void 0;
  		maxLineHeight = void 0;
  		defaultFontSize = 12;
  		fontToUnitRatio = defaultFontSize / this.pdf.internal.scaleFactor;
  		this.priorMarginBottom = this.priorMarginBottom || 0;
  		paragraphspacing_before = (Math.max((blockstyle["margin-top"] || 0) - this.priorMarginBottom, 0) + (blockstyle["padding-top"] || 0)) * fontToUnitRatio;
  		paragraphspacing_after = ((blockstyle["margin-bottom"] || 0) + (blockstyle["padding-bottom"] || 0)) * fontToUnitRatio;
  		this.priorMarginBottom = blockstyle["margin-bottom"] || 0;

  		if (blockstyle['page-break-before'] === 'always') {
  			this.pdf.addPage();
  			this.y = 0;
  			paragraphspacing_before = ((blockstyle["margin-top"] || 0) + (blockstyle["padding-top"] || 0)) * fontToUnitRatio;
  		}

  		out = this.pdf.internal.write;
  		i = void 0;
  		l = void 0;
  		this.y += paragraphspacing_before;
  		out("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), "Td");

  		//stores the current indent of cursor position
  		var currentIndent = 0;

  		while (lines.length) {
  			line = lines.shift();
  			maxLineHeight = 0;
  			i = 0;
  			l = line.length;
  			while (i !== l) {
  				if (line[i][0].trim()) {
  					maxLineHeight = Math.max(maxLineHeight, line[i][1]["line-height"], line[i][1]["font-size"]);
  					fontSize = line[i][1]["font-size"] * 7;
  				}
  				i++;
  			}
  			//if we have to move the cursor to adapt the indent
  			var indentMove = 0;
  			var wantedIndent = 0;
  			//if a margin was added (by e.g. a text-alignment), move the cursor
  			if (line[0][1]["margin-left"] !== undefined && line[0][1]["margin-left"] > 0) {
  				wantedIndent = this.pdf.internal.getCoordinateString(line[0][1]["margin-left"]);
  				indentMove = wantedIndent - currentIndent;
  				currentIndent = wantedIndent;
  			}
  			var indentMore = Math.max(blockstyle["margin-left"] || 0, 0) * fontToUnitRatio;
  			//move the cursor
  			out(indentMove + indentMore, (-1 * defaultFontSize * maxLineHeight).toFixed(2), "Td");
  			i = 0;
  			l = line.length;
  			while (i !== l) {
  				if (line[i][0]) {
  					this.RenderTextFragment(line[i][0], line[i][1]);
  				}
  				i++;
  			}
  			this.y += maxLineHeight * fontToUnitRatio;

  			//if some watcher function was executed successful, so e.g. margin and widths were changed,
  			//reset line drawing and calculate position and lines again
  			//e.g. to stop text floating around an image
  			if (this.executeWatchFunctions(line[0][1]) && lines.length > 0) {
  				var localFragments = [];
  				var localStyles = [];
  				//create fragment array of
  				lines.forEach(function (localLine) {
  					var i = 0;
  					var l = localLine.length;
  					while (i !== l) {
  						if (localLine[i][0]) {
  							localFragments.push(localLine[i][0] + ' ');
  							localStyles.push(localLine[i][1]);
  						}
  						++i;
  					}
  				});
  				//split lines again due to possible coordinate changes
  				lines = this.splitFragmentsIntoLines(PurgeWhiteSpace(localFragments), localStyles);
  				//reposition the current cursor
  				out("ET", "Q");
  				out("q", "BT 0 g", this.pdf.internal.getCoordinateString(this.x), this.pdf.internal.getVerticalCoordinateString(this.y), "Td");
  			}
  		}
  		if (cb && typeof cb === "function") {
  			cb.call(this, this.x - 9, this.y - fontSize / 2);
  		}
  		out("ET", "Q");
  		return this.y += paragraphspacing_after;
  	};
  	Renderer.prototype.setBlockBoundary = function (cb) {
  		return this.renderParagraph(cb);
  	};
  	Renderer.prototype.setBlockStyle = function (css) {
  		return this.paragraph.blockstyle = css;
  	};
  	Renderer.prototype.addText = function (text, css) {
  		this.paragraph.text.push(text);
  		return this.paragraph.style.push(css);
  	};
  	FontNameDB = {
  		helvetica: "helvetica",
  		"sans-serif": "helvetica",
  		"times new roman": "times",
  		serif: "times",
  		times: "times",
  		monospace: "courier",
  		courier: "courier"
  	};
  	FontWeightMap = {
  		100: "normal",
  		200: "normal",
  		300: "normal",
  		400: "normal",
  		500: "bold",
  		600: "bold",
  		700: "bold",
  		800: "bold",
  		900: "bold",
  		normal: "normal",
  		bold: "bold",
  		bolder: "bold",
  		lighter: "normal"
  	};
  	FontStyleMap = {
  		normal: "normal",
  		italic: "italic",
  		oblique: "italic"
  	};
  	TextAlignMap = {
  		left: "left",
  		right: "right",
  		center: "center",
  		justify: "justify"
  	};
  	FloatMap = {
  		none: 'none',
  		right: 'right',
  		left: 'left'
  	};
  	ClearMap = {
  		none: 'none',
  		both: 'both'
  	};
  	UnitedNumberMap = {
  		normal: 1
  	};
  	/**
    * Converts HTML-formatted text into formatted PDF text.
    *
    * Notes:
    * 2012-07-18
    * Plugin relies on having browser, DOM around. The HTML is pushed into dom and traversed.
    * Plugin relies on jQuery for CSS extraction.
    * Targeting HTML output from Markdown templating, which is a very simple
    * markup - div, span, em, strong, p. No br-based paragraph separation supported explicitly (but still may work.)
    * Images, tables are NOT supported.
    *
    * @public
    * @function
    * @param HTML {String or DOM Element} HTML-formatted text, or pointer to DOM element that is to be rendered into PDF.
    * @param x {Number} starting X coordinate in jsPDF instance's declared units.
    * @param y {Number} starting Y coordinate in jsPDF instance's declared units.
    * @param settings {Object} Additional / optional variables controlling parsing, rendering.
    * @returns {Object} jsPDF instance
    */
  	jsPDFAPI.fromHTML = function (HTML, x, y, settings, callback, margins) {
  		"use strict";

  		this.margins_doc = margins || {
  			top: 0,
  			bottom: 0
  		};
  		if (!settings) settings = {};
  		if (!settings.elementHandlers) settings.elementHandlers = {};

  		return process(this, HTML, isNaN(x) ? 4 : x, isNaN(y) ? 4 : y, settings, callback);
  	};
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF JavaScript plugin
   * Copyright (c) 2013 Youssef Beddad, youssef.beddad@gmail.com
   * 
   * 
   * ====================================================================
   */

  /*global jsPDF */

  (function (jsPDFAPI) {
      'use strict';

      var jsNamesObj, jsJsObj, text;
      jsPDFAPI.addJS = function (txt) {
          text = txt;
          this.internal.events.subscribe('postPutResources', function (txt) {
              jsNamesObj = this.internal.newObject();
              this.internal.write('<< /Names [(EmbeddedJS) ' + (jsNamesObj + 1) + ' 0 R] >>', 'endobj');
              jsJsObj = this.internal.newObject();
              this.internal.write('<< /S /JavaScript /JS (', text, ') >>', 'endobj');
          });
          this.internal.events.subscribe('putCatalog', function () {
              if (jsNamesObj !== undefined && jsJsObj !== undefined) {
                  this.internal.write('/Names <</JavaScript ' + jsNamesObj + ' 0 R>>');
              }
          });
          return this;
      };
  })(jsPDF.API);

  /**
   * jsPDF Outline PlugIn
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * Generates a PDF Outline
   */
  ;
  (function (jsPDFAPI) {
  	'use strict';

  	jsPDFAPI.events.push(['postPutResources', function () {
  		var pdf = this;
  		var rx = /^(\d+) 0 obj$/;

  		// Write action goto objects for each page
  		// this.outline.destsGoto = [];
  		// for (var i = 0; i < totalPages; i++) {
  		// var id = pdf.internal.newObject();
  		// this.outline.destsGoto.push(id);
  		// pdf.internal.write("<</D[" + (i * 2 + 3) + " 0 R /XYZ null
  		// null null]/S/GoTo>> endobj");
  		// }
  		//
  		// for (var i = 0; i < dests.length; i++) {
  		// pdf.internal.write("(page_" + (i + 1) + ")" + dests[i] + " 0
  		// R");
  		// }
  		//				
  		if (this.outline.root.children.length > 0) {
  			var lines = pdf.outline.render().split(/\r\n/);
  			for (var i = 0; i < lines.length; i++) {
  				var line = lines[i];
  				var m = rx.exec(line);
  				if (m != null) {
  					var oid = m[1];
  					pdf.internal.newObjectDeferredBegin(oid);
  				}
  				pdf.internal.write(line);
  			}
  		}

  		// This code will write named destination for each page reference
  		// (page_1, etc)
  		if (this.outline.createNamedDestinations) {
  			var totalPages = this.internal.pages.length;
  			// WARNING: this assumes jsPDF starts on page 3 and pageIDs
  			// follow 5, 7, 9, etc
  			// Write destination objects for each page
  			var dests = [];
  			for (var i = 0; i < totalPages; i++) {
  				var id = pdf.internal.newObject();
  				dests.push(id);
  				var info = pdf.internal.getPageInfo(i + 1);
  				pdf.internal.write("<< /D[" + info.objId + " 0 R /XYZ null null null]>> endobj");
  			}

  			// assign a name for each destination
  			var names2Oid = pdf.internal.newObject();
  			pdf.internal.write('<< /Names [ ');
  			for (var i = 0; i < dests.length; i++) {
  				pdf.internal.write("(page_" + (i + 1) + ")" + dests[i] + " 0 R");
  			}
  			pdf.internal.write(' ] >>', 'endobj');

  			// var kids = pdf.internal.newObject();
  			// pdf.internal.write('<< /Kids [ ' + names2Oid + ' 0 R');
  			// pdf.internal.write(' ] >>', 'endobj');

  			var namesOid = pdf.internal.newObject();
  			pdf.internal.write('<< /Dests ' + names2Oid + " 0 R");
  			pdf.internal.write('>>', 'endobj');
  		}
  	}]);

  	jsPDFAPI.events.push(['putCatalog', function () {
  		var pdf = this;
  		if (pdf.outline.root.children.length > 0) {
  			pdf.internal.write("/Outlines", this.outline.makeRef(this.outline.root));
  			if (this.outline.createNamedDestinations) {
  				pdf.internal.write("/Names " + namesOid + " 0 R");
  			}
  			// Open with Bookmarks showing
  			// pdf.internal.write("/PageMode /UseOutlines");
  		}
  	}]);

  	jsPDFAPI.events.push(['initialized', function () {
  		var pdf = this;

  		pdf.outline = {
  			createNamedDestinations: false,
  			root: {
  				children: []
  			}
  		};

  		var namesOid;
  		var destsGoto = [];

  		/**
     * Options: pageNumber
     */
  		pdf.outline.add = function (parent, title, options) {
  			var item = {
  				title: title,
  				options: options,
  				children: []
  			};
  			if (parent == null) {
  				parent = this.root;
  			}
  			parent.children.push(item);
  			return item;
  		};

  		pdf.outline.render = function () {
  			this.ctx = {};
  			this.ctx.val = '';
  			this.ctx.pdf = pdf;

  			this.genIds_r(this.root);
  			this.renderRoot(this.root);
  			this.renderItems(this.root);

  			return this.ctx.val;
  		};

  		pdf.outline.genIds_r = function (node) {
  			node.id = pdf.internal.newObjectDeferred();
  			for (var i = 0; i < node.children.length; i++) {
  				this.genIds_r(node.children[i]);
  			}
  		};

  		pdf.outline.renderRoot = function (node) {
  			this.objStart(node);
  			this.line('/Type /Outlines');
  			if (node.children.length > 0) {
  				this.line('/First ' + this.makeRef(node.children[0]));
  				this.line('/Last ' + this.makeRef(node.children[node.children.length - 1]));
  			}
  			this.line('/Count ' + this.count_r({
  				count: 0
  			}, node));
  			this.objEnd();
  		};

  		pdf.outline.renderItems = function (node) {
  			for (var i = 0; i < node.children.length; i++) {
  				var item = node.children[i];
  				this.objStart(item);

  				this.line('/Title ' + this.makeString(item.title));

  				this.line('/Parent ' + this.makeRef(node));
  				if (i > 0) {
  					this.line('/Prev ' + this.makeRef(node.children[i - 1]));
  				}
  				if (i < node.children.length - 1) {
  					this.line('/Next ' + this.makeRef(node.children[i + 1]));
  				}
  				if (item.children.length > 0) {
  					this.line('/First ' + this.makeRef(item.children[0]));
  					this.line('/Last ' + this.makeRef(item.children[item.children.length - 1]));
  				}

  				var count = this.count = this.count_r({
  					count: 0
  				}, item);
  				if (count > 0) {
  					this.line('/Count ' + count);
  				}

  				if (item.options) {
  					if (item.options.pageNumber) {
  						// Explicit Destination
  						//WARNING this assumes page ids are 3,5,7, etc.
  						var info = pdf.internal.getPageInfo(item.options.pageNumber);
  						this.line('/Dest ' + '[' + info.objId + ' 0 R /XYZ 0 ' + this.ctx.pdf.internal.pageSize.height + ' 0]');
  						// this line does not work on all clients (pageNumber instead of page ref)
  						//this.line('/Dest ' + '[' + (item.options.pageNumber - 1) + ' /XYZ 0 ' + this.ctx.pdf.internal.pageSize.height + ' 0]');

  						// Named Destination
  						// this.line('/Dest (page_' + (item.options.pageNumber) + ')');

  						// Action Destination
  						// var id = pdf.internal.newObject();
  						// pdf.internal.write('<</D[' + (item.options.pageNumber - 1) + ' /XYZ null null null]/S/GoTo>> endobj');
  						// this.line('/A ' + id + ' 0 R' );
  					}
  				}
  				this.objEnd();
  			}
  			for (var i = 0; i < node.children.length; i++) {
  				var item = node.children[i];
  				this.renderItems(item);
  			}
  		};

  		pdf.outline.line = function (text) {
  			this.ctx.val += text + '\r\n';
  		};

  		pdf.outline.makeRef = function (node) {
  			return node.id + ' 0 R';
  		};

  		pdf.outline.makeString = function (val) {
  			return '(' + pdf.internal.pdfEscape(val) + ')';
  		};

  		pdf.outline.objStart = function (node) {
  			this.ctx.val += '\r\n' + node.id + ' 0 obj' + '\r\n<<\r\n';
  		};

  		pdf.outline.objEnd = function (node) {
  			this.ctx.val += '>> \r\n' + 'endobj' + '\r\n';
  		};

  		pdf.outline.count_r = function (ctx, node) {
  			for (var i = 0; i < node.children.length; i++) {
  				ctx.count++;
  				this.count_r(ctx, node.children[i]);
  			}
  			return ctx.count;
  		};
  	}]);

  	return this;
  })(jsPDF.API);

  /**@preserve
   *  ====================================================================
   * jsPDF PNG PlugIn
   * Copyright (c) 2014 James Robb, https://github.com/jamesbrobb
   *
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
  	'use strict';

  	/*
    * @see http://www.w3.org/TR/PNG-Chunks.html
    *
    Color    Allowed      Interpretation
    Type     Bit Depths
   	   0       1,2,4,8,16  Each pixel is a grayscale sample.
   	   2       8,16        Each pixel is an R,G,B triple.
   	   3       1,2,4,8     Each pixel is a palette index;
                          a PLTE chunk must appear.
   	   4       8,16        Each pixel is a grayscale sample,
                          followed by an alpha sample.
   	   6       8,16        Each pixel is an R,G,B triple,
                          followed by an alpha sample.
   */

  	/*
    * PNG filter method types
    *
    * @see http://www.w3.org/TR/PNG-Filters.html
    * @see http://www.libpng.org/pub/png/book/chapter09.html
    *
    * This is what the value 'Predictor' in decode params relates to
    *
    * 15 is "optimal prediction", which means the prediction algorithm can change from line to line.
    * In that case, you actually have to read the first byte off each line for the prediction algorthim (which should be 0-4, corresponding to PDF 10-14) and select the appropriate unprediction algorithm based on that byte.
    *
      0       None
      1       Sub
      2       Up
      3       Average
      4       Paeth
    */

  	var doesNotHavePngJS = function doesNotHavePngJS() {
  		return typeof PNG !== 'function' || typeof FlateStream !== 'function';
  	},
  	    canCompress = function canCompress(value) {
  		return value !== jsPDFAPI.image_compression.NONE && hasCompressionJS();
  	},
  	    hasCompressionJS = function hasCompressionJS() {
  		var inst = typeof Deflater === 'function';
  		if (!inst) throw new Error("requires deflate.js for compression");
  		return inst;
  	},
  	    compressBytes = function compressBytes(bytes, lineLength, colorsPerPixel, compression) {

  		var level = 5,
  		    filter_method = filterUp;

  		switch (compression) {

  			case jsPDFAPI.image_compression.FAST:

  				level = 3;
  				filter_method = filterSub;
  				break;

  			case jsPDFAPI.image_compression.MEDIUM:

  				level = 6;
  				filter_method = filterAverage;
  				break;

  			case jsPDFAPI.image_compression.SLOW:

  				level = 9;
  				filter_method = filterPaeth; //uses to sum to choose best filter for each line
  				break;
  		}

  		bytes = applyPngFilterMethod(bytes, lineLength, colorsPerPixel, filter_method);

  		var header = new Uint8Array(createZlibHeader(level));
  		var checksum = adler32(bytes);

  		var deflate = new Deflater(level);
  		var a = deflate.append(bytes);
  		var cBytes = deflate.flush();

  		var len = header.length + a.length + cBytes.length;

  		var cmpd = new Uint8Array(len + 4);
  		cmpd.set(header);
  		cmpd.set(a, header.length);
  		cmpd.set(cBytes, header.length + a.length);

  		cmpd[len++] = checksum >>> 24 & 0xff;
  		cmpd[len++] = checksum >>> 16 & 0xff;
  		cmpd[len++] = checksum >>> 8 & 0xff;
  		cmpd[len++] = checksum & 0xff;

  		return jsPDFAPI.arrayBufferToBinaryString(cmpd);
  	},
  	    createZlibHeader = function createZlibHeader(bytes, level) {
  		/*
     * @see http://www.ietf.org/rfc/rfc1950.txt for zlib header
     */
  		var cm = 8;
  		var cinfo = Math.LOG2E * Math.log(0x8000) - 8;
  		var cmf = cinfo << 4 | cm;

  		var hdr = cmf << 8;
  		var flevel = Math.min(3, (level - 1 & 0xff) >> 1);

  		hdr |= flevel << 6;
  		hdr |= 0; //FDICT
  		hdr += 31 - hdr % 31;

  		return [cmf, hdr & 0xff & 0xff];
  	},
  	    adler32 = function adler32(array, param) {
  		var adler = 1;
  		var s1 = adler & 0xffff,
  		    s2 = adler >>> 16 & 0xffff;
  		var len = array.length;
  		var tlen;
  		var i = 0;

  		while (len > 0) {
  			tlen = len > param ? param : len;
  			len -= tlen;
  			do {
  				s1 += array[i++];
  				s2 += s1;
  			} while (--tlen);

  			s1 %= 65521;
  			s2 %= 65521;
  		}

  		return (s2 << 16 | s1) >>> 0;
  	},
  	    applyPngFilterMethod = function applyPngFilterMethod(bytes, lineLength, colorsPerPixel, filter_method) {
  		var lines = bytes.length / lineLength,
  		    result = new Uint8Array(bytes.length + lines),
  		    filter_methods = getFilterMethods(),
  		    i = 0,
  		    line,
  		    prevLine,
  		    offset;

  		for (; i < lines; i++) {
  			offset = i * lineLength;
  			line = bytes.subarray(offset, offset + lineLength);

  			if (filter_method) {
  				result.set(filter_method(line, colorsPerPixel, prevLine), offset + i);
  			} else {

  				var j = 0,
  				    len = filter_methods.length,
  				    results = [];

  				for (; j < len; j++) {
  					results[j] = filter_methods[j](line, colorsPerPixel, prevLine);
  				}var ind = getIndexOfSmallestSum(results.concat());

  				result.set(results[ind], offset + i);
  			}

  			prevLine = line;
  		}

  		return result;
  	},
  	    filterNone = function filterNone(line, colorsPerPixel, prevLine) {
  		/*var result = new Uint8Array(line.length + 1);
    result[0] = 0;
    result.set(line, 1);*/

  		var result = Array.apply([], line);
  		result.unshift(0);

  		return result;
  	},
  	    filterSub = function filterSub(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left;

  		result[0] = 1;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			result[i + 1] = line[i] - left + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    filterUp = function filterUp(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    up;

  		result[0] = 2;

  		for (; i < len; i++) {
  			up = prevLine && prevLine[i] || 0;
  			result[i + 1] = line[i] - up + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    filterAverage = function filterAverage(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left,
  		    up;

  		result[0] = 3;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			up = prevLine && prevLine[i] || 0;
  			result[i + 1] = line[i] + 0x0100 - (left + up >>> 1) & 0xff;
  		}

  		return result;
  	},
  	    filterPaeth = function filterPaeth(line, colorsPerPixel, prevLine) {
  		var result = [],
  		    i = 0,
  		    len = line.length,
  		    left,
  		    up,
  		    upLeft,
  		    paeth;

  		result[0] = 4;

  		for (; i < len; i++) {
  			left = line[i - colorsPerPixel] || 0;
  			up = prevLine && prevLine[i] || 0;
  			upLeft = prevLine && prevLine[i - colorsPerPixel] || 0;
  			paeth = paethPredictor(left, up, upLeft);
  			result[i + 1] = line[i] - paeth + 0x0100 & 0xff;
  		}

  		return result;
  	},
  	    paethPredictor = function paethPredictor(left, up, upLeft) {

  		var p = left + up - upLeft,
  		    pLeft = Math.abs(p - left),
  		    pUp = Math.abs(p - up),
  		    pUpLeft = Math.abs(p - upLeft);

  		return pLeft <= pUp && pLeft <= pUpLeft ? left : pUp <= pUpLeft ? up : upLeft;
  	},
  	    getFilterMethods = function getFilterMethods() {
  		return [filterNone, filterSub, filterUp, filterAverage, filterPaeth];
  	},
  	    getIndexOfSmallestSum = function getIndexOfSmallestSum(arrays) {
  		var i = 0,
  		    len = arrays.length,
  		    sum,
  		    min,
  		    ind;

  		while (i < len) {
  			sum = absSum(arrays[i].slice(1));

  			if (sum < min || !min) {
  				min = sum;
  				ind = i;
  			}

  			i++;
  		}

  		return ind;
  	},
  	    absSum = function absSum(array) {
  		var i = 0,
  		    len = array.length,
  		    sum = 0;

  		while (i < len) {
  			sum += Math.abs(array[i++]);
  		}return sum;
  	},
  	    getPredictorFromCompression = function getPredictorFromCompression(compression) {
  		var predictor;
  		switch (compression) {
  			case jsPDFAPI.image_compression.FAST:
  				predictor = 11;
  				break;

  			case jsPDFAPI.image_compression.MEDIUM:
  				predictor = 13;
  				break;

  			case jsPDFAPI.image_compression.SLOW:
  				predictor = 14;
  				break;
  		}
  		return predictor;
  	},
  	    logImg = function logImg(img) {
  		console.log("width: " + img.width);
  		console.log("height: " + img.height);
  		console.log("bits: " + img.bits);
  		console.log("colorType: " + img.colorType);
  		console.log("transparency:");
  		console.log(img.transparency);
  		console.log("text:");
  		console.log(img.text);
  		console.log("compressionMethod: " + img.compressionMethod);
  		console.log("filterMethod: " + img.filterMethod);
  		console.log("interlaceMethod: " + img.interlaceMethod);
  		console.log("imgData:");
  		console.log(img.imgData);
  		console.log("palette:");
  		console.log(img.palette);
  		console.log("colors: " + img.colors);
  		console.log("colorSpace: " + img.colorSpace);
  		console.log("pixelBitlength: " + img.pixelBitlength);
  		console.log("hasAlphaChannel: " + img.hasAlphaChannel);
  	};

  	jsPDFAPI.processPNG = function (imageData, imageIndex, alias, compression, dataAsBinaryString) {
  		'use strict';

  		var colorSpace = this.color_spaces.DEVICE_RGB,
  		    decode = this.decode.FLATE_DECODE,
  		    bpc = 8,
  		    img,
  		    dp,
  		    trns,
  		    colors,
  		    pal,
  		    smask;

  		/*	if(this.isString(imageData)) {
    		}*/

  		if (this.isArrayBuffer(imageData)) imageData = new Uint8Array(imageData);

  		if (this.isArrayBufferView(imageData)) {

  			if (doesNotHavePngJS()) throw new Error("PNG support requires png.js and zlib.js");

  			img = new PNG(imageData);
  			imageData = img.imgData;
  			bpc = img.bits;
  			colorSpace = img.colorSpace;
  			colors = img.colors;

  			//logImg(img);

  			/*
      * colorType 6 - Each pixel is an R,G,B triple, followed by an alpha sample.
      *
      * colorType 4 - Each pixel is a grayscale sample, followed by an alpha sample.
      *
      * Extract alpha to create two separate images, using the alpha as a sMask
      */
  			if ([4, 6].indexOf(img.colorType) !== -1) {

  				/*
       * processes 8 bit RGBA and grayscale + alpha images
       */
  				if (img.bits === 8) {

  					var pixels = img.pixelBitlength == 32 ? new Uint32Array(img.decodePixels().buffer) : img.pixelBitlength == 16 ? new Uint16Array(img.decodePixels().buffer) : new Uint8Array(img.decodePixels().buffer),
  					    len = pixels.length,
  					    imgData = new Uint8Array(len * img.colors),
  					    alphaData = new Uint8Array(len),
  					    pDiff = img.pixelBitlength - img.bits,
  					    i = 0,
  					    n = 0,
  					    pixel,
  					    pbl;

  					for (; i < len; i++) {
  						pixel = pixels[i];
  						pbl = 0;

  						while (pbl < pDiff) {

  							imgData[n++] = pixel >>> pbl & 0xff;
  							pbl = pbl + img.bits;
  						}

  						alphaData[i] = pixel >>> pbl & 0xff;
  					}
  				}

  				/*
       * processes 16 bit RGBA and grayscale + alpha images
       */
  				if (img.bits === 16) {

  					var pixels = new Uint32Array(img.decodePixels().buffer),
  					    len = pixels.length,
  					    imgData = new Uint8Array(len * (32 / img.pixelBitlength) * img.colors),
  					    alphaData = new Uint8Array(len * (32 / img.pixelBitlength)),
  					    hasColors = img.colors > 1,
  					    i = 0,
  					    n = 0,
  					    a = 0,
  					    pixel;

  					while (i < len) {
  						pixel = pixels[i++];

  						imgData[n++] = pixel >>> 0 & 0xFF;

  						if (hasColors) {
  							imgData[n++] = pixel >>> 16 & 0xFF;

  							pixel = pixels[i++];
  							imgData[n++] = pixel >>> 0 & 0xFF;
  						}

  						alphaData[a++] = pixel >>> 16 & 0xFF;
  					}

  					bpc = 8;
  				}

  				if (canCompress(compression)) {

  					imageData = compressBytes(imgData, img.width * img.colors, img.colors, compression);
  					smask = compressBytes(alphaData, img.width, 1, compression);
  				} else {

  					imageData = imgData;
  					smask = alphaData;
  					decode = null;
  				}
  			}

  			/*
      * Indexed png. Each pixel is a palette index.
      */
  			if (img.colorType === 3) {

  				colorSpace = this.color_spaces.INDEXED;
  				pal = img.palette;

  				if (img.transparency.indexed) {

  					var trans = img.transparency.indexed;

  					var total = 0,
  					    i = 0,
  					    len = trans.length;

  					for (; i < len; ++i) {
  						total += trans[i];
  					}total = total / 255;

  					/*
        * a single color is specified as 100% transparent (0),
        * so we set trns to use a /Mask with that index
        */
  					if (total === len - 1 && trans.indexOf(0) !== -1) {
  						trns = [trans.indexOf(0)];

  						/*
         * there's more than one colour within the palette that specifies
         * a transparency value less than 255, so we unroll the pixels to create an image sMask
         */
  					} else if (total !== len) {

  						var pixels = img.decodePixels(),
  						    alphaData = new Uint8Array(pixels.length),
  						    i = 0,
  						    len = pixels.length;

  						for (; i < len; i++) {
  							alphaData[i] = trans[pixels[i]];
  						}smask = compressBytes(alphaData, img.width, 1);
  					}
  				}
  			}

  			var predictor = getPredictorFromCompression(compression);

  			if (decode === this.decode.FLATE_DECODE) dp = '/Predictor ' + predictor + ' /Colors ' + colors + ' /BitsPerComponent ' + bpc + ' /Columns ' + img.width;else
  				//remove 'Predictor' as it applies to the type of png filter applied to its IDAT - we only apply with compression
  				dp = '/Colors ' + colors + ' /BitsPerComponent ' + bpc + ' /Columns ' + img.width;

  			if (this.isArrayBuffer(imageData) || this.isArrayBufferView(imageData)) imageData = this.arrayBufferToBinaryString(imageData);

  			if (smask && this.isArrayBuffer(smask) || this.isArrayBufferView(smask)) smask = this.arrayBufferToBinaryString(smask);

  			return this.createImageInfo(imageData, img.width, img.height, colorSpace, bpc, decode, imageIndex, alias, dp, trns, pal, smask, predictor);
  		}

  		throw new Error("Unsupported PNG image data, try using JPEG instead.");
  	};
  })(jsPDF.API);

  /**
   * jsPDF Autoprint Plugin
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  (function (jsPDFAPI) {
    'use strict';

    jsPDFAPI.autoPrint = function () {
      'use strict';

      var refAutoPrintTag;

      this.internal.events.subscribe('postPutResources', function () {
        refAutoPrintTag = this.internal.newObject();
        this.internal.write("<< /S/Named /Type/Action /N/Print >>", "endobj");
      });

      this.internal.events.subscribe("putCatalog", function () {
        this.internal.write("/OpenAction " + refAutoPrintTag + " 0" + " R");
      });
      return this;
    };
  })(jsPDF.API);

  /** @preserve
   * jsPDF split_text_to_size plugin - MIT license.
   * Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
   *               2014 Diego Casorran, https://github.com/diegocr
   */
  /**
   * 
   * ====================================================================
   */

  ;(function (API) {
  	'use strict';

  	/**
   Returns an array of length matching length of the 'word' string, with each
   cell ocupied by the width of the char in that position.
   
   @function
   @param word {String}
   @param widths {Object}
   @param kerning {Object}
   @returns {Array}
   */

  	var getCharWidthsArray = API.getCharWidthsArray = function (text, options) {

  		if (!options) {
  			options = {};
  		}

  		var widths = options.widths ? options.widths : this.internal.getFont().metadata.Unicode.widths,
  		    widthsFractionOf = widths.fof ? widths.fof : 1,
  		    kerning = options.kerning ? options.kerning : this.internal.getFont().metadata.Unicode.kerning,
  		    kerningFractionOf = kerning.fof ? kerning.fof : 1;

  		// console.log("widths, kergnings", widths, kerning)

  		var i,
  		    l,
  		    char_code,
  		    prior_char_code = 0 // for kerning
  		,
  		    default_char_width = widths[0] || widthsFractionOf,
  		    output = [];

  		for (i = 0, l = text.length; i < l; i++) {
  			char_code = text.charCodeAt(i);
  			output.push((widths[char_code] || default_char_width) / widthsFractionOf + (kerning[char_code] && kerning[char_code][prior_char_code] || 0) / kerningFractionOf);
  			prior_char_code = char_code;
  		}

  		return output;
  	};
  	var getArraySum = function getArraySum(array) {
  		var i = array.length,
  		    output = 0;
  		while (i) {
  			;i--;
  			output += array[i];
  		}
  		return output;
  	};
  	/**
   Returns a widths of string in a given font, if the font size is set as 1 point.
   
   In other words, this is "proportional" value. For 1 unit of font size, the length
   of the string will be that much.
   
   Multiply by font size to get actual width in *points*
   Then divide by 72 to get inches or divide by (72/25.6) to get 'mm' etc.
   
   @public
   @function
   @param
   @returns {Type}
   */
  	var getStringUnitWidth = API.getStringUnitWidth = function (text, options) {
  		return getArraySum(getCharWidthsArray.call(this, text, options));
  	};

  	/**
   returns array of lines
   */
  	var splitLongWord = function splitLongWord(word, widths_array, firstLineMaxLen, maxLen) {
  		var answer = [];

  		// 1st, chop off the piece that can fit on the hanging line.
  		var i = 0,
  		    l = word.length,
  		    workingLen = 0;
  		while (i !== l && workingLen + widths_array[i] < firstLineMaxLen) {
  			workingLen += widths_array[i];i++;
  		}
  		// this is first line.
  		answer.push(word.slice(0, i));

  		// 2nd. Split the rest into maxLen pieces.
  		var startOfLine = i;
  		workingLen = 0;
  		while (i !== l) {
  			if (workingLen + widths_array[i] > maxLen) {
  				answer.push(word.slice(startOfLine, i));
  				workingLen = 0;
  				startOfLine = i;
  			}
  			workingLen += widths_array[i];i++;
  		}
  		if (startOfLine !== i) {
  			answer.push(word.slice(startOfLine, i));
  		}

  		return answer;
  	};

  	// Note, all sizing inputs for this function must be in "font measurement units"
  	// By default, for PDF, it's "point".
  	var splitParagraphIntoLines = function splitParagraphIntoLines(text, maxlen, options) {
  		// at this time works only on Western scripts, ones with space char
  		// separating the words. Feel free to expand.

  		if (!options) {
  			options = {};
  		}

  		var line = [],
  		    lines = [line],
  		    line_length = options.textIndent || 0,
  		    separator_length = 0,
  		    current_word_length = 0,
  		    word,
  		    widths_array,
  		    words = text.split(' '),
  		    spaceCharWidth = getCharWidthsArray(' ', options)[0],
  		    i,
  		    l,
  		    tmp,
  		    lineIndent;

  		if (options.lineIndent === -1) {
  			lineIndent = words[0].length + 2;
  		} else {
  			lineIndent = options.lineIndent || 0;
  		}
  		if (lineIndent) {
  			var pad = Array(lineIndent).join(" "),
  			    wrds = [];
  			words.map(function (wrd) {
  				wrd = wrd.split(/\s*\n/);
  				if (wrd.length > 1) {
  					wrds = wrds.concat(wrd.map(function (wrd, idx) {
  						return (idx && wrd.length ? "\n" : "") + wrd;
  					}));
  				} else {
  					wrds.push(wrd[0]);
  				}
  			});
  			words = wrds;
  			lineIndent = getStringUnitWidth(pad, options);
  		}

  		for (i = 0, l = words.length; i < l; i++) {
  			var force = 0;

  			word = words[i];
  			if (lineIndent && word[0] == "\n") {
  				word = word.substr(1);
  				force = 1;
  			}
  			widths_array = getCharWidthsArray(word, options);
  			current_word_length = getArraySum(widths_array);

  			if (line_length + separator_length + current_word_length > maxlen || force) {
  				if (current_word_length > maxlen) {
  					// this happens when you have space-less long URLs for example.
  					// we just chop these to size. We do NOT insert hiphens
  					tmp = splitLongWord(word, widths_array, maxlen - (line_length + separator_length), maxlen);
  					// first line we add to existing line object
  					line.push(tmp.shift()); // it's ok to have extra space indicator there
  					// last line we make into new line object
  					line = [tmp.pop()];
  					// lines in the middle we apped to lines object as whole lines
  					while (tmp.length) {
  						lines.push([tmp.shift()]); // single fragment occupies whole line
  					}
  					current_word_length = getArraySum(widths_array.slice(word.length - line[0].length));
  				} else {
  					// just put it on a new line
  					line = [word];
  				}

  				// now we attach new line to lines
  				lines.push(line);
  				line_length = current_word_length + lineIndent;
  				separator_length = spaceCharWidth;
  			} else {
  				line.push(word);

  				line_length += separator_length + current_word_length;
  				separator_length = spaceCharWidth;
  			}
  		}

  		if (lineIndent) {
  			var postProcess = function postProcess(ln, idx) {
  				return (idx ? pad : '') + ln.join(" ");
  			};
  		} else {
  			var postProcess = function postProcess(ln) {
  				return ln.join(" ");
  			};
  		}

  		return lines.map(postProcess);
  	};

  	/**
   Splits a given string into an array of strings. Uses 'size' value
   (in measurement units declared as default for the jsPDF instance)
   and the font's "widths" and "Kerning" tables, where available, to
   determine display length of a given string for a given font.
   
   We use character's 100% of unit size (height) as width when Width
   table or other default width is not available.
   
   @public
   @function
   @param text {String} Unencoded, regular JavaScript (Unicode, UTF-16 / UCS-2) string.
   @param size {Number} Nominal number, measured in units default to this instance of jsPDF.
   @param options {Object} Optional flags needed for chopper to do the right thing.
   @returns {Array} with strings chopped to size.
   */
  	API.splitTextToSize = function (text, maxlen, options) {
  		'use strict';

  		if (!options) {
  			options = {};
  		}

  		var fsize = options.fontSize || this.internal.getFontSize(),
  		    newOptions = function (options) {
  			var widths = { 0: 1 },
  			    kerning = {};

  			if (!options.widths || !options.kerning) {
  				var f = this.internal.getFont(options.fontName, options.fontStyle),
  				    encoding = 'Unicode';
  				// NOT UTF8, NOT UTF16BE/LE, NOT UCS2BE/LE
  				// Actual JavaScript-native String's 16bit char codes used.
  				// no multi-byte logic here

  				if (f.metadata[encoding]) {
  					return {
  						widths: f.metadata[encoding].widths || widths,
  						kerning: f.metadata[encoding].kerning || kerning
  					};
  				}
  			} else {
  				return {
  					widths: options.widths,
  					kerning: options.kerning
  				};
  			}

  			// then use default values
  			return {
  				widths: widths,
  				kerning: kerning
  			};
  		}.call(this, options);

  		// first we split on end-of-line chars
  		var paragraphs;
  		if (Array.isArray(text)) {
  			paragraphs = text;
  		} else {
  			paragraphs = text.split(/\r?\n/);
  		}

  		// now we convert size (max length of line) into "font size units"
  		// at present time, the "font size unit" is always 'point'
  		// 'proportional' means, "in proportion to font size"
  		var fontUnit_maxLen = 1.0 * this.internal.scaleFactor * maxlen / fsize;
  		// at this time, fsize is always in "points" regardless of the default measurement unit of the doc.
  		// this may change in the future?
  		// until then, proportional_maxlen is likely to be in 'points'

  		// If first line is to be indented (shorter or longer) than maxLen
  		// we indicate that by using CSS-style "text-indent" option.
  		// here it's in font units too (which is likely 'points')
  		// it can be negative (which makes the first line longer than maxLen)
  		newOptions.textIndent = options.textIndent ? options.textIndent * 1.0 * this.internal.scaleFactor / fsize : 0;
  		newOptions.lineIndent = options.lineIndent;

  		var i,
  		    l,
  		    output = [];
  		for (i = 0, l = paragraphs.length; i < l; i++) {
  			output = output.concat(splitParagraphIntoLines(paragraphs[i], fontUnit_maxLen, newOptions));
  		}

  		return output;
  	};
  })(jsPDF.API);

  /** @preserve 
  jsPDF standard_fonts_metrics plugin
  Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
  MIT license.
  */
  /**
   * 
   * ====================================================================
   */

  ;(function (API) {
  	'use strict';

  	/*
   # reference (Python) versions of 'compress' and 'uncompress'
   # only 'uncompress' function is featured lower as JavaScript
   # if you want to unit test "roundtrip", just transcribe the reference
   # 'compress' function from Python into JavaScript
   
   def compress(data):
   
   	keys =   '0123456789abcdef'
   	values = 'klmnopqrstuvwxyz'
   	mapping = dict(zip(keys, values))
   	vals = []
   	for key in data.keys():
   		value = data[key]
   		try:
   			keystring = hex(key)[2:]
   			keystring = keystring[:-1] + mapping[keystring[-1:]]
   		except:
   			keystring = key.join(["'","'"])
   			#print('Keystring is %s' % keystring)
   
   		try:
   			if value < 0:
   				valuestring = hex(value)[3:]
   				numberprefix = '-'
   			else:
   				valuestring = hex(value)[2:]
   				numberprefix = ''
   			valuestring = numberprefix + valuestring[:-1] + mapping[valuestring[-1:]]
   		except:
   			if type(value) == dict:
   				valuestring = compress(value)
   			else:
   				raise Exception("Don't know what to do with value type %s" % type(value))
   
   		vals.append(keystring+valuestring)
   	
   	return '{' + ''.join(vals) + '}'
   
   def uncompress(data):
   
   	decoded = '0123456789abcdef'
   	encoded = 'klmnopqrstuvwxyz'
   	mapping = dict(zip(encoded, decoded))
   
   	sign = +1
   	stringmode = False
   	stringparts = []
   
   	output = {}
   
   	activeobject = output
   	parentchain = []
   
   	keyparts = ''
   	valueparts = ''
   
   	key = None
   
   	ending = set(encoded)
   
   	i = 1
   	l = len(data) - 1 # stripping starting, ending {}
   	while i != l: # stripping {}
   		# -, {, }, ' are special.
   
   		ch = data[i]
   		i += 1
   
   		if ch == "'":
   			if stringmode:
   				# end of string mode
   				stringmode = False
   				key = ''.join(stringparts)
   			else:
   				# start of string mode
   				stringmode = True
   				stringparts = []
   		elif stringmode == True:
   			#print("Adding %s to stringpart" % ch)
   			stringparts.append(ch)
   
   		elif ch == '{':
   			# start of object
   			parentchain.append( [activeobject, key] )
   			activeobject = {}
   			key = None
   			#DEBUG = True
   		elif ch == '}':
   			# end of object
   			parent, key = parentchain.pop()
   			parent[key] = activeobject
   			key = None
   			activeobject = parent
   			#DEBUG = False
   
   		elif ch == '-':
   			sign = -1
   		else:
   			# must be number
   			if key == None:
   				#debug("In Key. It is '%s', ch is '%s'" % (keyparts, ch))
   				if ch in ending:
   					#debug("End of key")
   					keyparts += mapping[ch]
   					key = int(keyparts, 16) * sign
   					sign = +1
   					keyparts = ''
   				else:
   					keyparts += ch
   			else:
   				#debug("In value. It is '%s', ch is '%s'" % (valueparts, ch))
   				if ch in ending:
   					#debug("End of value")
   					valueparts += mapping[ch]
   					activeobject[key] = int(valueparts, 16) * sign
   					sign = +1
   					key = None
   					valueparts = ''
   				else:
   					valueparts += ch
   
   			#debug(activeobject)
   
   	return output
   
   */

  	/**
   Uncompresses data compressed into custom, base16-like format. 
   @public
   @function
   @param
   @returns {Type}
   */

  	var uncompress = function uncompress(data) {

  		var decoded = '0123456789abcdef',
  		    encoded = 'klmnopqrstuvwxyz',
  		    mapping = {};

  		for (var i = 0; i < encoded.length; i++) {
  			mapping[encoded[i]] = decoded[i];
  		}

  		var undef,
  		    output = {},
  		    sign = 1,
  		    stringparts // undef. will be [] in string mode

  		,
  		    activeobject = output,
  		    parentchain = [],
  		    parent_key_pair,
  		    keyparts = '',
  		    valueparts = '',
  		    key // undef. will be Truthy when Key is resolved.
  		,
  		    datalen = data.length - 1 // stripping ending }
  		,
  		    ch;

  		i = 1; // stripping starting {

  		while (i != datalen) {
  			// - { } ' are special.

  			ch = data[i];
  			i += 1;

  			if (ch == "'") {
  				if (stringparts) {
  					// end of string mode
  					key = stringparts.join('');
  					stringparts = undef;
  				} else {
  					// start of string mode
  					stringparts = [];
  				}
  			} else if (stringparts) {
  				stringparts.push(ch);
  			} else if (ch == '{') {
  				// start of object
  				parentchain.push([activeobject, key]);
  				activeobject = {};
  				key = undef;
  			} else if (ch == '}') {
  				// end of object
  				parent_key_pair = parentchain.pop();
  				parent_key_pair[0][parent_key_pair[1]] = activeobject;
  				key = undef;
  				activeobject = parent_key_pair[0];
  			} else if (ch == '-') {
  				sign = -1;
  			} else {
  				// must be number
  				if (key === undef) {
  					if (mapping.hasOwnProperty(ch)) {
  						keyparts += mapping[ch];
  						key = parseInt(keyparts, 16) * sign;
  						sign = +1;
  						keyparts = '';
  					} else {
  						keyparts += ch;
  					}
  				} else {
  					if (mapping.hasOwnProperty(ch)) {
  						valueparts += mapping[ch];
  						activeobject[key] = parseInt(valueparts, 16) * sign;
  						sign = +1;
  						key = undef;
  						valueparts = '';
  					} else {
  						valueparts += ch;
  					}
  				}
  			}
  		} // end while

  		return output;
  	};

  	// encoding = 'Unicode' 
  	// NOT UTF8, NOT UTF16BE/LE, NOT UCS2BE/LE. NO clever BOM behavior
  	// Actual 16bit char codes used.
  	// no multi-byte logic here

  	// Unicode characters to WinAnsiEncoding:
  	// {402: 131, 8211: 150, 8212: 151, 8216: 145, 8217: 146, 8218: 130, 8220: 147, 8221: 148, 8222: 132, 8224: 134, 8225: 135, 8226: 149, 8230: 133, 8364: 128, 8240:137, 8249: 139, 8250: 155, 710: 136, 8482: 153, 338: 140, 339: 156, 732: 152, 352: 138, 353: 154, 376: 159, 381: 142, 382: 158}
  	// as you can see, all Unicode chars are outside of 0-255 range. No char code conflicts.
  	// this means that you can give Win cp1252 encoded strings to jsPDF for rendering directly
  	// as well as give strings with some (supported by these fonts) Unicode characters and 
  	// these will be mapped to win cp1252 
  	// for example, you can send char code (cp1252) 0x80 or (unicode) 0x20AC, getting "Euro" glyph displayed in both cases.

  	var encodingBlock = {
  		'codePages': ['WinAnsiEncoding'],
  		'WinAnsiEncoding': uncompress("{19m8n201n9q201o9r201s9l201t9m201u8m201w9n201x9o201y8o202k8q202l8r202m9p202q8p20aw8k203k8t203t8v203u9v2cq8s212m9t15m8w15n9w2dw9s16k8u16l9u17s9z17x8y17y9y}")
  	},
  	    encodings = { 'Unicode': {
  			'Courier': encodingBlock,
  			'Courier-Bold': encodingBlock,
  			'Courier-BoldOblique': encodingBlock,
  			'Courier-Oblique': encodingBlock,
  			'Helvetica': encodingBlock,
  			'Helvetica-Bold': encodingBlock,
  			'Helvetica-BoldOblique': encodingBlock,
  			'Helvetica-Oblique': encodingBlock,
  			'Times-Roman': encodingBlock,
  			'Times-Bold': encodingBlock,
  			'Times-BoldItalic': encodingBlock,
  			'Times-Italic': encodingBlock
  			//	, 'Symbol'
  			//	, 'ZapfDingbats'
  		} }
  	/** 
   Resources:
   Font metrics data is reprocessed derivative of contents of
   "Font Metrics for PDF Core 14 Fonts" package, which exhibits the following copyright and license:
   
   Copyright (c) 1989, 1990, 1991, 1992, 1993, 1997 Adobe Systems Incorporated. All Rights Reserved.
   
   This file and the 14 PostScript(R) AFM files it accompanies may be used,
   copied, and distributed for any purpose and without charge, with or without
   modification, provided that all copyright notices are retained; that the AFM
   files are not distributed without this file; that all modifications to this
   file or any of the AFM files are prominently noted in the modified file(s);
   and that this paragraph is not modified. Adobe Systems has no responsibility
   or obligation to support the use of the AFM files.
   
   */
  	,
  	    fontMetrics = { 'Unicode': {
  			// all sizing numbers are n/fontMetricsFractionOf = one font size unit
  			// this means that if fontMetricsFractionOf = 1000, and letter A's width is 476, it's
  			// width is 476/1000 or 47.6% of its height (regardless of font size)
  			// At this time this value applies to "widths" and "kerning" numbers.

  			// char code 0 represents "default" (average) width - use it for chars missing in this table.
  			// key 'fof' represents the "fontMetricsFractionOf" value

  			'Courier-Oblique': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-BoldItalic': uncompress("{'widths'{k3o2q4ycx2r201n3m201o6o201s2l201t2l201u2l201w3m201x3m201y3m2k1t2l2r202m2n2n3m2o3m2p5n202q6o2r1w2s2l2t2l2u3m2v3t2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w3t3x3t3y3t3z3m4k5n4l4m4m4m4n4m4o4s4p4m4q4m4r4s4s4y4t2r4u3m4v4m4w3x4x5t4y4s4z4s5k3x5l4s5m4m5n3r5o3x5p4s5q4m5r5t5s4m5t3x5u3x5v2l5w1w5x2l5y3t5z3m6k2l6l3m6m3m6n2w6o3m6p2w6q2l6r3m6s3r6t1w6u1w6v3m6w1w6x4y6y3r6z3m7k3m7l3m7m2r7n2r7o1w7p3r7q2w7r4m7s3m7t2w7u2r7v2n7w1q7x2n7y3t202l3mcl4mal2ram3man3mao3map3mar3mas2lat4uau1uav3maw3way4uaz2lbk2sbl3t'fof'6obo2lbp3tbq3mbr1tbs2lbu1ybv3mbz3mck4m202k3mcm4mcn4mco4mcp4mcq5ycr4mcs4mct4mcu4mcv4mcw2r2m3rcy2rcz2rdl4sdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek3mel3mem3men3meo3mep3meq4ser2wes2wet2weu2wev2wew1wex1wey1wez1wfl3rfm3mfn3mfo3mfp3mfq3mfr3tfs3mft3rfu3rfv3rfw3rfz2w203k6o212m6o2dw2l2cq2l3t3m3u2l17s3x19m3m}'kerning'{cl{4qu5kt5qt5rs17ss5ts}201s{201ss}201t{cks4lscmscnscoscpscls2wu2yu201ts}201x{2wu2yu}2k{201ts}2w{4qx5kx5ou5qx5rs17su5tu}2x{17su5tu5ou}2y{4qx5kx5ou5qx5rs17ss5ts}'fof'-6ofn{17sw5tw5ou5qw5rs}7t{cksclscmscnscoscps4ls}3u{17su5tu5os5qs}3v{17su5tu5os5qs}7p{17su5tu}ck{4qu5kt5qt5rs17ss5ts}4l{4qu5kt5qt5rs17ss5ts}cm{4qu5kt5qt5rs17ss5ts}cn{4qu5kt5qt5rs17ss5ts}co{4qu5kt5qt5rs17ss5ts}cp{4qu5kt5qt5rs17ss5ts}6l{4qu5ou5qw5rt17su5tu}5q{ckuclucmucnucoucpu4lu}5r{ckuclucmucnucoucpu4lu}7q{cksclscmscnscoscps4ls}6p{4qu5ou5qw5rt17sw5tw}ek{4qu5ou5qw5rt17su5tu}el{4qu5ou5qw5rt17su5tu}em{4qu5ou5qw5rt17su5tu}en{4qu5ou5qw5rt17su5tu}eo{4qu5ou5qw5rt17su5tu}ep{4qu5ou5qw5rt17su5tu}es{17ss5ts5qs4qu}et{4qu5ou5qw5rt17sw5tw}eu{4qu5ou5qw5rt17ss5ts}ev{17ss5ts5qs4qu}6z{17sw5tw5ou5qw5rs}fm{17sw5tw5ou5qw5rs}7n{201ts}fo{17sw5tw5ou5qw5rs}fp{17sw5tw5ou5qw5rs}fq{17sw5tw5ou5qw5rs}7r{cksclscmscnscoscps4ls}fs{17sw5tw5ou5qw5rs}ft{17su5tu}fu{17su5tu}fv{17su5tu}fw{17su5tu}fz{cksclscmscnscoscps4ls}}}"),
  			'Helvetica-Bold': uncompress("{'widths'{k3s2q4scx1w201n3r201o6o201s1w201t1w201u1w201w3m201x3m201y3m2k1w2l2l202m2n2n3r2o3r2p5t202q6o2r1s2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v2l3w3u3x3u3y3u3z3x4k6l4l4s4m4s4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3r4v4s4w3x4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v2l5w1w5x2l5y3u5z3r6k2l6l3r6m3x6n3r6o3x6p3r6q2l6r3x6s3x6t1w6u1w6v3r6w1w6x5t6y3x6z3x7k3x7l3x7m2r7n3r7o2l7p3x7q3r7r4y7s3r7t3r7u3m7v2r7w1w7x2r7y3u202l3rcl4sal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3xbq3rbr1wbs2lbu2obv3rbz3xck4s202k3rcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw1w2m2zcy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3res3ret3reu3rev3rew1wex1wey1wez1wfl3xfm3xfn3xfo3xfp3xfq3xfr3ufs3xft3xfu3xfv3xfw3xfz3r203k6o212m6o2dw2l2cq2l3t3r3u2l17s4m19m3r}'kerning'{cl{4qs5ku5ot5qs17sv5tv}201t{2ww4wy2yw}201w{2ks}201x{2ww4wy2yw}2k{201ts201xs}2w{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}2x{5ow5qs}2y{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}'fof'-6o7p{17su5tu5ot}ck{4qs5ku5ot5qs17sv5tv}4l{4qs5ku5ot5qs17sv5tv}cm{4qs5ku5ot5qs17sv5tv}cn{4qs5ku5ot5qs17sv5tv}co{4qs5ku5ot5qs17sv5tv}cp{4qs5ku5ot5qs17sv5tv}6l{17st5tt5os}17s{2kwclvcmvcnvcovcpv4lv4wwckv}5o{2kucltcmtcntcotcpt4lt4wtckt}5q{2ksclscmscnscoscps4ls4wvcks}5r{2ks4ws}5t{2kwclvcmvcnvcovcpv4lv4wwckv}eo{17st5tt5os}fu{17su5tu5ot}6p{17ss5ts}ek{17st5tt5os}el{17st5tt5os}em{17st5tt5os}en{17st5tt5os}6o{201ts}ep{17st5tt5os}es{17ss5ts}et{17ss5ts}eu{17ss5ts}ev{17ss5ts}6z{17su5tu5os5qt}fm{17su5tu5os5qt}fn{17su5tu5os5qt}fo{17su5tu5os5qt}fp{17su5tu5os5qt}fq{17su5tu5os5qt}fs{17su5tu5os5qt}ft{17su5tu5ot}7m{5os}fv{17su5tu5ot}fw{17su5tu5ot}}}"),
  			'Courier': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Courier-BoldOblique': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-Bold': uncompress("{'widths'{k3q2q5ncx2r201n3m201o6o201s2l201t2l201u2l201w3m201x3m201y3m2k1t2l2l202m2n2n3m2o3m2p6o202q6o2r1w2s2l2t2l2u3m2v3t2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w3t3x3t3y3t3z3m4k5x4l4s4m4m4n4s4o4s4p4m4q3x4r4y4s4y4t2r4u3m4v4y4w4m4x5y4y4s4z4y5k3x5l4y5m4s5n3r5o4m5p4s5q4s5r6o5s4s5t4s5u4m5v2l5w1w5x2l5y3u5z3m6k2l6l3m6m3r6n2w6o3r6p2w6q2l6r3m6s3r6t1w6u2l6v3r6w1w6x5n6y3r6z3m7k3r7l3r7m2w7n2r7o2l7p3r7q3m7r4s7s3m7t3m7u2w7v2r7w1q7x2r7y3o202l3mcl4sal2lam3man3mao3map3mar3mas2lat4uau1yav3maw3tay4uaz2lbk2sbl3t'fof'6obo2lbp3rbr1tbs2lbu2lbv3mbz3mck4s202k3mcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw2r2m3rcy2rcz2rdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3rek3mel3mem3men3meo3mep3meq4ser2wes2wet2weu2wev2wew1wex1wey1wez1wfl3rfm3mfn3mfo3mfp3mfq3mfr3tfs3mft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3m3u2l17s4s19m3m}'kerning'{cl{4qt5ks5ot5qy5rw17sv5tv}201t{cks4lscmscnscoscpscls4wv}2k{201ts}2w{4qu5ku7mu5os5qx5ru17su5tu}2x{17su5tu5ou5qs}2y{4qv5kv7mu5ot5qz5ru17su5tu}'fof'-6o7t{cksclscmscnscoscps4ls}3u{17su5tu5os5qu}3v{17su5tu5os5qu}fu{17su5tu5ou5qu}7p{17su5tu5ou5qu}ck{4qt5ks5ot5qy5rw17sv5tv}4l{4qt5ks5ot5qy5rw17sv5tv}cm{4qt5ks5ot5qy5rw17sv5tv}cn{4qt5ks5ot5qy5rw17sv5tv}co{4qt5ks5ot5qy5rw17sv5tv}cp{4qt5ks5ot5qy5rw17sv5tv}6l{17st5tt5ou5qu}17s{ckuclucmucnucoucpu4lu4wu}5o{ckuclucmucnucoucpu4lu4wu}5q{ckzclzcmzcnzcozcpz4lz4wu}5r{ckxclxcmxcnxcoxcpx4lx4wu}5t{ckuclucmucnucoucpu4lu4wu}7q{ckuclucmucnucoucpu4lu}6p{17sw5tw5ou5qu}ek{17st5tt5qu}el{17st5tt5ou5qu}em{17st5tt5qu}en{17st5tt5qu}eo{17st5tt5qu}ep{17st5tt5ou5qu}es{17ss5ts5qu}et{17sw5tw5ou5qu}eu{17sw5tw5ou5qu}ev{17ss5ts5qu}6z{17sw5tw5ou5qu5rs}fm{17sw5tw5ou5qu5rs}fn{17sw5tw5ou5qu5rs}fo{17sw5tw5ou5qu5rs}fp{17sw5tw5ou5qu5rs}fq{17sw5tw5ou5qu5rs}7r{cktcltcmtcntcotcpt4lt5os}fs{17sw5tw5ou5qu5rs}ft{17su5tu5ou5qu}7m{5os}fv{17su5tu5ou5qu}fw{17su5tu5ou5qu}fz{cksclscmscnscoscps4ls}}}")
  			//, 'Symbol': uncompress("{'widths'{k3uaw4r19m3m2k1t2l2l202m2y2n3m2p5n202q6o3k3m2s2l2t2l2v3r2w1t3m3m2y1t2z1wbk2sbl3r'fof'6o3n3m3o3m3p3m3q3m3r3m3s3m3t3m3u1w3v1w3w3r3x3r3y3r3z2wbp3t3l3m5v2l5x2l5z3m2q4yfr3r7v3k7w1o7x3k}'kerning'{'fof'-6o}}")
  			, 'Helvetica': uncompress("{'widths'{k3p2q4mcx1w201n3r201o6o201s1q201t1q201u1q201w2l201x2l201y2l2k1w2l1w202m2n2n3r2o3r2p5t202q6o2r1n2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v1w3w3u3x3u3y3u3z3r4k6p4l4m4m4m4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3m4v4m4w3r4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v1w5w1w5x1w5y2z5z3r6k2l6l3r6m3r6n3m6o3r6p3r6q1w6r3r6s3r6t1q6u1q6v3m6w1q6x5n6y3r6z3r7k3r7l3r7m2l7n3m7o1w7p3r7q3m7r4s7s3m7t3m7u3m7v2l7w1u7x2l7y3u202l3rcl4mal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3rbr1wbs2lbu2obv3rbz3xck4m202k3rcm4mcn4mco4mcp4mcq6ocr4scs4mct4mcu4mcv4mcw1w2m2ncy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3mes3ret3reu3rev3rew1wex1wey1wez1wfl3rfm3rfn3rfo3rfp3rfq3rfr3ufs3xft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3r3u1w17s4m19m3r}'kerning'{5q{4wv}cl{4qs5kw5ow5qs17sv5tv}201t{2wu4w1k2yu}201x{2wu4wy2yu}17s{2ktclucmucnu4otcpu4lu4wycoucku}2w{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}2x{17sy5ty5oy5qs}2y{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}'fof'-6o7p{17sv5tv5ow}ck{4qs5kw5ow5qs17sv5tv}4l{4qs5kw5ow5qs17sv5tv}cm{4qs5kw5ow5qs17sv5tv}cn{4qs5kw5ow5qs17sv5tv}co{4qs5kw5ow5qs17sv5tv}cp{4qs5kw5ow5qs17sv5tv}6l{17sy5ty5ow}do{17st5tt}4z{17st5tt}7s{fst}dm{17st5tt}dn{17st5tt}5o{ckwclwcmwcnwcowcpw4lw4wv}dp{17st5tt}dq{17st5tt}7t{5ow}ds{17st5tt}5t{2ktclucmucnu4otcpu4lu4wycoucku}fu{17sv5tv5ow}6p{17sy5ty5ow5qs}ek{17sy5ty5ow}el{17sy5ty5ow}em{17sy5ty5ow}en{5ty}eo{17sy5ty5ow}ep{17sy5ty5ow}es{17sy5ty5qs}et{17sy5ty5ow5qs}eu{17sy5ty5ow5qs}ev{17sy5ty5ow5qs}6z{17sy5ty5ow5qs}fm{17sy5ty5ow5qs}fn{17sy5ty5ow5qs}fo{17sy5ty5ow5qs}fp{17sy5ty5qs}fq{17sy5ty5ow5qs}7r{5ow}fs{17sy5ty5ow5qs}ft{17sv5tv5ow}7m{5ow}fv{17sv5tv5ow}fw{17sv5tv5ow}}}"),
  			'Helvetica-BoldOblique': uncompress("{'widths'{k3s2q4scx1w201n3r201o6o201s1w201t1w201u1w201w3m201x3m201y3m2k1w2l2l202m2n2n3r2o3r2p5t202q6o2r1s2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v2l3w3u3x3u3y3u3z3x4k6l4l4s4m4s4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3r4v4s4w3x4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v2l5w1w5x2l5y3u5z3r6k2l6l3r6m3x6n3r6o3x6p3r6q2l6r3x6s3x6t1w6u1w6v3r6w1w6x5t6y3x6z3x7k3x7l3x7m2r7n3r7o2l7p3x7q3r7r4y7s3r7t3r7u3m7v2r7w1w7x2r7y3u202l3rcl4sal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3xbq3rbr1wbs2lbu2obv3rbz3xck4s202k3rcm4scn4sco4scp4scq6ocr4scs4mct4mcu4mcv4mcw1w2m2zcy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3res3ret3reu3rev3rew1wex1wey1wez1wfl3xfm3xfn3xfo3xfp3xfq3xfr3ufs3xft3xfu3xfv3xfw3xfz3r203k6o212m6o2dw2l2cq2l3t3r3u2l17s4m19m3r}'kerning'{cl{4qs5ku5ot5qs17sv5tv}201t{2ww4wy2yw}201w{2ks}201x{2ww4wy2yw}2k{201ts201xs}2w{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}2x{5ow5qs}2y{7qs4qu5kw5os5qw5rs17su5tu7tsfzs}'fof'-6o7p{17su5tu5ot}ck{4qs5ku5ot5qs17sv5tv}4l{4qs5ku5ot5qs17sv5tv}cm{4qs5ku5ot5qs17sv5tv}cn{4qs5ku5ot5qs17sv5tv}co{4qs5ku5ot5qs17sv5tv}cp{4qs5ku5ot5qs17sv5tv}6l{17st5tt5os}17s{2kwclvcmvcnvcovcpv4lv4wwckv}5o{2kucltcmtcntcotcpt4lt4wtckt}5q{2ksclscmscnscoscps4ls4wvcks}5r{2ks4ws}5t{2kwclvcmvcnvcovcpv4lv4wwckv}eo{17st5tt5os}fu{17su5tu5ot}6p{17ss5ts}ek{17st5tt5os}el{17st5tt5os}em{17st5tt5os}en{17st5tt5os}6o{201ts}ep{17st5tt5os}es{17ss5ts}et{17ss5ts}eu{17ss5ts}ev{17ss5ts}6z{17su5tu5os5qt}fm{17su5tu5os5qt}fn{17su5tu5os5qt}fo{17su5tu5os5qt}fp{17su5tu5os5qt}fq{17su5tu5os5qt}fs{17su5tu5os5qt}ft{17su5tu5ot}7m{5os}fv{17su5tu5ot}fw{17su5tu5ot}}}")
  			//, 'ZapfDingbats': uncompress("{'widths'{k4u2k1w'fof'6o}'kerning'{'fof'-6o}}")
  			, 'Courier-Bold': uncompress("{'widths'{k3w'fof'6o}'kerning'{'fof'-6o}}"),
  			'Times-Italic': uncompress("{'widths'{k3n2q4ycx2l201n3m201o5t201s2l201t2l201u2l201w3r201x3r201y3r2k1t2l2l202m2n2n3m2o3m2p5n202q5t2r1p2s2l2t2l2u3m2v4n2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v2l3w4n3x4n3y4n3z3m4k5w4l3x4m3x4n4m4o4s4p3x4q3x4r4s4s4s4t2l4u2w4v4m4w3r4x5n4y4m4z4s5k3x5l4s5m3x5n3m5o3r5p4s5q3x5r5n5s3x5t3r5u3r5v2r5w1w5x2r5y2u5z3m6k2l6l3m6m3m6n2w6o3m6p2w6q1w6r3m6s3m6t1w6u1w6v2w6w1w6x4s6y3m6z3m7k3m7l3m7m2r7n2r7o1w7p3m7q2w7r4m7s2w7t2w7u2r7v2s7w1v7x2s7y3q202l3mcl3xal2ram3man3mao3map3mar3mas2lat4wau1vav3maw4nay4waz2lbk2sbl4n'fof'6obo2lbp3mbq3obr1tbs2lbu1zbv3mbz3mck3x202k3mcm3xcn3xco3xcp3xcq5tcr4mcs3xct3xcu3xcv3xcw2l2m2ucy2lcz2ldl4mdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek3mel3mem3men3meo3mep3meq4mer2wes2wet2weu2wev2wew1wex1wey1wez1wfl3mfm3mfn3mfo3mfp3mfq3mfr4nfs3mft3mfu3mfv3mfw3mfz2w203k6o212m6m2dw2l2cq2l3t3m3u2l17s3r19m3m}'kerning'{cl{5kt4qw}201s{201sw}201t{201tw2wy2yy6q-t}201x{2wy2yy}2k{201tw}2w{7qs4qy7rs5ky7mw5os5qx5ru17su5tu}2x{17ss5ts5os}2y{7qs4qy7rs5ky7mw5os5qx5ru17su5tu}'fof'-6o6t{17ss5ts5qs}7t{5os}3v{5qs}7p{17su5tu5qs}ck{5kt4qw}4l{5kt4qw}cm{5kt4qw}cn{5kt4qw}co{5kt4qw}cp{5kt4qw}6l{4qs5ks5ou5qw5ru17su5tu}17s{2ks}5q{ckvclvcmvcnvcovcpv4lv}5r{ckuclucmucnucoucpu4lu}5t{2ks}6p{4qs5ks5ou5qw5ru17su5tu}ek{4qs5ks5ou5qw5ru17su5tu}el{4qs5ks5ou5qw5ru17su5tu}em{4qs5ks5ou5qw5ru17su5tu}en{4qs5ks5ou5qw5ru17su5tu}eo{4qs5ks5ou5qw5ru17su5tu}ep{4qs5ks5ou5qw5ru17su5tu}es{5ks5qs4qs}et{4qs5ks5ou5qw5ru17su5tu}eu{4qs5ks5qw5ru17su5tu}ev{5ks5qs4qs}ex{17ss5ts5qs}6z{4qv5ks5ou5qw5ru17su5tu}fm{4qv5ks5ou5qw5ru17su5tu}fn{4qv5ks5ou5qw5ru17su5tu}fo{4qv5ks5ou5qw5ru17su5tu}fp{4qv5ks5ou5qw5ru17su5tu}fq{4qv5ks5ou5qw5ru17su5tu}7r{5os}fs{4qv5ks5ou5qw5ru17su5tu}ft{17su5tu5qs}fu{17su5tu5qs}fv{17su5tu5qs}fw{17su5tu5qs}}}"),
  			'Times-Roman': uncompress("{'widths'{k3n2q4ycx2l201n3m201o6o201s2l201t2l201u2l201w2w201x2w201y2w2k1t2l2l202m2n2n3m2o3m2p5n202q6o2r1m2s2l2t2l2u3m2v3s2w1t2x2l2y1t2z1w3k3m3l3m3m3m3n3m3o3m3p3m3q3m3r3m3s3m203t2l203u2l3v1w3w3s3x3s3y3s3z2w4k5w4l4s4m4m4n4m4o4s4p3x4q3r4r4s4s4s4t2l4u2r4v4s4w3x4x5t4y4s4z4s5k3r5l4s5m4m5n3r5o3x5p4s5q4s5r5y5s4s5t4s5u3x5v2l5w1w5x2l5y2z5z3m6k2l6l2w6m3m6n2w6o3m6p2w6q2l6r3m6s3m6t1w6u1w6v3m6w1w6x4y6y3m6z3m7k3m7l3m7m2l7n2r7o1w7p3m7q3m7r4s7s3m7t3m7u2w7v3k7w1o7x3k7y3q202l3mcl4sal2lam3man3mao3map3mar3mas2lat4wau1vav3maw3say4waz2lbk2sbl3s'fof'6obo2lbp3mbq2xbr1tbs2lbu1zbv3mbz2wck4s202k3mcm4scn4sco4scp4scq5tcr4mcs3xct3xcu3xcv3xcw2l2m2tcy2lcz2ldl4sdm4sdn4sdo4sdp4sdq4sds4sdt4sdu4sdv4sdw4sdz3mek2wel2wem2wen2weo2wep2weq4mer2wes2wet2weu2wev2wew1wex1wey1wez1wfl3mfm3mfn3mfo3mfp3mfq3mfr3sfs3mft3mfu3mfv3mfw3mfz3m203k6o212m6m2dw2l2cq2l3t3m3u1w17s4s19m3m}'kerning'{cl{4qs5ku17sw5ou5qy5rw201ss5tw201ws}201s{201ss}201t{ckw4lwcmwcnwcowcpwclw4wu201ts}2k{201ts}2w{4qs5kw5os5qx5ru17sx5tx}2x{17sw5tw5ou5qu}2y{4qs5kw5os5qx5ru17sx5tx}'fof'-6o7t{ckuclucmucnucoucpu4lu5os5rs}3u{17su5tu5qs}3v{17su5tu5qs}7p{17sw5tw5qs}ck{4qs5ku17sw5ou5qy5rw201ss5tw201ws}4l{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cm{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cn{4qs5ku17sw5ou5qy5rw201ss5tw201ws}co{4qs5ku17sw5ou5qy5rw201ss5tw201ws}cp{4qs5ku17sw5ou5qy5rw201ss5tw201ws}6l{17su5tu5os5qw5rs}17s{2ktclvcmvcnvcovcpv4lv4wuckv}5o{ckwclwcmwcnwcowcpw4lw4wu}5q{ckyclycmycnycoycpy4ly4wu5ms}5r{cktcltcmtcntcotcpt4lt4ws}5t{2ktclvcmvcnvcovcpv4lv4wuckv}7q{cksclscmscnscoscps4ls}6p{17su5tu5qw5rs}ek{5qs5rs}el{17su5tu5os5qw5rs}em{17su5tu5os5qs5rs}en{17su5qs5rs}eo{5qs5rs}ep{17su5tu5os5qw5rs}es{5qs}et{17su5tu5qw5rs}eu{17su5tu5qs5rs}ev{5qs}6z{17sv5tv5os5qx5rs}fm{5os5qt5rs}fn{17sv5tv5os5qx5rs}fo{17sv5tv5os5qx5rs}fp{5os5qt5rs}fq{5os5qt5rs}7r{ckuclucmucnucoucpu4lu5os}fs{17sv5tv5os5qx5rs}ft{17ss5ts5qs}fu{17sw5tw5qs}fv{17sw5tw5qs}fw{17ss5ts5qs}fz{ckuclucmucnucoucpu4lu5os5rs}}}"),
  			'Helvetica-Oblique': uncompress("{'widths'{k3p2q4mcx1w201n3r201o6o201s1q201t1q201u1q201w2l201x2l201y2l2k1w2l1w202m2n2n3r2o3r2p5t202q6o2r1n2s2l2t2l2u2r2v3u2w1w2x2l2y1w2z1w3k3r3l3r3m3r3n3r3o3r3p3r3q3r3r3r3s3r203t2l203u2l3v1w3w3u3x3u3y3u3z3r4k6p4l4m4m4m4n4s4o4s4p4m4q3x4r4y4s4s4t1w4u3m4v4m4w3r4x5n4y4s4z4y5k4m5l4y5m4s5n4m5o3x5p4s5q4m5r5y5s4m5t4m5u3x5v1w5w1w5x1w5y2z5z3r6k2l6l3r6m3r6n3m6o3r6p3r6q1w6r3r6s3r6t1q6u1q6v3m6w1q6x5n6y3r6z3r7k3r7l3r7m2l7n3m7o1w7p3r7q3m7r4s7s3m7t3m7u3m7v2l7w1u7x2l7y3u202l3rcl4mal2lam3ran3rao3rap3rar3ras2lat4tau2pav3raw3uay4taz2lbk2sbl3u'fof'6obo2lbp3rbr1wbs2lbu2obv3rbz3xck4m202k3rcm4mcn4mco4mcp4mcq6ocr4scs4mct4mcu4mcv4mcw1w2m2ncy1wcz1wdl4sdm4ydn4ydo4ydp4ydq4yds4ydt4sdu4sdv4sdw4sdz3xek3rel3rem3ren3reo3rep3req5ter3mes3ret3reu3rev3rew1wex1wey1wez1wfl3rfm3rfn3rfo3rfp3rfq3rfr3ufs3xft3rfu3rfv3rfw3rfz3m203k6o212m6o2dw2l2cq2l3t3r3u1w17s4m19m3r}'kerning'{5q{4wv}cl{4qs5kw5ow5qs17sv5tv}201t{2wu4w1k2yu}201x{2wu4wy2yu}17s{2ktclucmucnu4otcpu4lu4wycoucku}2w{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}2x{17sy5ty5oy5qs}2y{7qs4qz5k1m17sy5ow5qx5rsfsu5ty7tufzu}'fof'-6o7p{17sv5tv5ow}ck{4qs5kw5ow5qs17sv5tv}4l{4qs5kw5ow5qs17sv5tv}cm{4qs5kw5ow5qs17sv5tv}cn{4qs5kw5ow5qs17sv5tv}co{4qs5kw5ow5qs17sv5tv}cp{4qs5kw5ow5qs17sv5tv}6l{17sy5ty5ow}do{17st5tt}4z{17st5tt}7s{fst}dm{17st5tt}dn{17st5tt}5o{ckwclwcmwcnwcowcpw4lw4wv}dp{17st5tt}dq{17st5tt}7t{5ow}ds{17st5tt}5t{2ktclucmucnu4otcpu4lu4wycoucku}fu{17sv5tv5ow}6p{17sy5ty5ow5qs}ek{17sy5ty5ow}el{17sy5ty5ow}em{17sy5ty5ow}en{5ty}eo{17sy5ty5ow}ep{17sy5ty5ow}es{17sy5ty5qs}et{17sy5ty5ow5qs}eu{17sy5ty5ow5qs}ev{17sy5ty5ow5qs}6z{17sy5ty5ow5qs}fm{17sy5ty5ow5qs}fn{17sy5ty5ow5qs}fo{17sy5ty5ow5qs}fp{17sy5ty5qs}fq{17sy5ty5ow5qs}7r{5ow}fs{17sy5ty5ow5qs}ft{17sv5tv5ow}7m{5ow}fv{17sv5tv5ow}fw{17sv5tv5ow}}}")
  		} };

  	/*
   This event handler is fired when a new jsPDF object is initialized
   This event handler appends metrics data to standard fonts within
   that jsPDF instance. The metrics are mapped over Unicode character
   codes, NOT CIDs or other codes matching the StandardEncoding table of the
   standard PDF fonts.
   Future:
   Also included is the encoding maping table, converting Unicode (UCS-2, UTF-16)
   char codes to StandardEncoding character codes. The encoding table is to be used
   somewhere around "pdfEscape" call.
   */

  	API.events.push(['addFont', function (font) {
  		var metrics,
  		    unicode_section,
  		    encoding = 'Unicode',
  		    encodingBlock;

  		metrics = fontMetrics[encoding][font.PostScriptName];
  		if (metrics) {
  			if (font.metadata[encoding]) {
  				unicode_section = font.metadata[encoding];
  			} else {
  				unicode_section = font.metadata[encoding] = {};
  			}

  			unicode_section.widths = metrics.widths;
  			unicode_section.kerning = metrics.kerning;
  		}

  		encodingBlock = encodings[encoding][font.PostScriptName];
  		if (encodingBlock) {
  			if (font.metadata[encoding]) {
  				unicode_section = font.metadata[encoding];
  			} else {
  				unicode_section = font.metadata[encoding] = {};
  			}

  			unicode_section.encoding = encodingBlock;
  			if (encodingBlock.codePages && encodingBlock.codePages.length) {
  				font.encoding = encodingBlock.codePages[0];
  			}
  		}
  	}]); // end of adding event handler
  })(jsPDF.API);

  /** @preserve
  jsPDF SVG plugin
  Copyright (c) 2012 Willow Systems Corporation, willow-systems.com
  */
  /**
   * 
   * ====================================================================
   */

  ;(function (jsPDFAPI) {
  	'use strict';

  	/**
   Parses SVG XML and converts only some of the SVG elements into
   PDF elements.
   
   Supports:
    paths
   
   @public
   @function
   @param
   @returns {Type}
   */

  	jsPDFAPI.addSVG = function (svgtext, x, y, w, h) {
  		// 'this' is _jsPDF object returned when jsPDF is inited (new jsPDF())

  		var undef;

  		if (x === undef || y === undef) {
  			throw new Error("addSVG needs values for 'x' and 'y'");
  		}

  		function InjectCSS(cssbody, document) {
  			var styletag = document.createElement('style');
  			styletag.type = 'text/css';
  			if (styletag.styleSheet) {
  				// ie
  				styletag.styleSheet.cssText = cssbody;
  			} else {
  				// others
  				styletag.appendChild(document.createTextNode(cssbody));
  			}
  			document.getElementsByTagName("head")[0].appendChild(styletag);
  		}

  		function createWorkerNode(document) {

  			var frameID = 'childframe' // Date.now().toString() + '_' + (Math.random() * 100).toString()
  			,
  			    frame = document.createElement('iframe');

  			InjectCSS('.jsPDF_sillysvg_iframe {display:none;position:absolute;}', document);

  			frame.name = frameID;
  			frame.setAttribute("width", 0);
  			frame.setAttribute("height", 0);
  			frame.setAttribute("frameborder", "0");
  			frame.setAttribute("scrolling", "no");
  			frame.setAttribute("seamless", "seamless");
  			frame.setAttribute("class", "jsPDF_sillysvg_iframe");

  			document.body.appendChild(frame);

  			return frame;
  		}

  		function attachSVGToWorkerNode(svgtext, frame) {
  			var framedoc = (frame.contentWindow || frame.contentDocument).document;
  			framedoc.write(svgtext);
  			framedoc.close();
  			return framedoc.getElementsByTagName('svg')[0];
  		}

  		function convertPathToPDFLinesArgs(path) {
  			'use strict';
  			// we will use 'lines' method call. it needs:
  			// - starting coordinate pair
  			// - array of arrays of vector shifts (2-len for line, 6 len for bezier)
  			// - scale array [horizontal, vertical] ratios
  			// - style (stroke, fill, both)

  			var x = parseFloat(path[1]),
  			    y = parseFloat(path[2]),
  			    vectors = [],
  			    position = 3,
  			    len = path.length;

  			while (position < len) {
  				if (path[position] === 'c') {
  					vectors.push([parseFloat(path[position + 1]), parseFloat(path[position + 2]), parseFloat(path[position + 3]), parseFloat(path[position + 4]), parseFloat(path[position + 5]), parseFloat(path[position + 6])]);
  					position += 7;
  				} else if (path[position] === 'l') {
  					vectors.push([parseFloat(path[position + 1]), parseFloat(path[position + 2])]);
  					position += 3;
  				} else {
  					position += 1;
  				}
  			}
  			return [x, y, vectors];
  		}

  		var workernode = createWorkerNode(document),
  		    svgnode = attachSVGToWorkerNode(svgtext, workernode),
  		    scale = [1, 1],
  		    svgw = parseFloat(svgnode.getAttribute('width')),
  		    svgh = parseFloat(svgnode.getAttribute('height'));

  		if (svgw && svgh) {
  			// setting both w and h makes image stretch to size.
  			// this may distort the image, but fits your demanded size
  			if (w && h) {
  				scale = [w / svgw, h / svgh];
  			}
  			// if only one is set, that value is set as max and SVG
  			// is scaled proportionately.
  			else if (w) {
  					scale = [w / svgw, w / svgw];
  				} else if (h) {
  					scale = [h / svgh, h / svgh];
  				}
  		}

  		var i,
  		    l,
  		    tmp,
  		    linesargs,
  		    items = svgnode.childNodes;
  		for (i = 0, l = items.length; i < l; i++) {
  			tmp = items[i];
  			if (tmp.tagName && tmp.tagName.toUpperCase() === 'PATH') {
  				linesargs = convertPathToPDFLinesArgs(tmp.getAttribute("d").split(' '));
  				// path start x coordinate
  				linesargs[0] = linesargs[0] * scale[0] + x; // where x is upper left X of image
  				// path start y coordinate
  				linesargs[1] = linesargs[1] * scale[1] + y; // where y is upper left Y of image
  				// the rest of lines are vectors. these will adjust with scale value auto.
  				this.lines.call(this, linesargs[2] // lines
  				, linesargs[0] // starting x
  				, linesargs[1] // starting y
  				, scale);
  			}
  		}

  		// clean up
  		// workernode.parentNode.removeChild(workernode)

  		return this;
  	};
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF total_pages plugin
   * Copyright (c) 2013 Eduardo Menezes de Morais, eduardo.morais@usp.br
   * 
   * 
   * ====================================================================
   */

  (function (jsPDFAPI) {
    'use strict';

    jsPDFAPI.putTotalPages = function (pageExpression) {
      'use strict';

      var replaceExpression = new RegExp(pageExpression, 'g');
      for (var n = 1; n <= this.internal.getNumberOfPages(); n++) {
        for (var i = 0; i < this.internal.pages[n].length; i++) {
          this.internal.pages[n][i] = this.internal.pages[n][i].replace(replaceExpression, this.internal.getNumberOfPages());
        }
      }
      return this;
    };
  })(jsPDF.API);

  /** ==================================================================== 
   * jsPDF XMP metadata plugin
   * Copyright (c) 2016 Jussi Utunen, u-jussi@suomi24.fi
   * 
   * 
   * ====================================================================
   */

  /*global jsPDF */

  /**
  * Adds XMP formatted metadata to PDF
  *
  * @param {String} metadata The actual metadata to be added. The metadata shall be stored as XMP simple value. Note that if the metadata string contains XML markup characters "<", ">" or "&", those characters should be written using XML entities.
  * @param {String} namespaceuri Sets the namespace URI for the metadata. Last character should be slash or hash.
  * @function
  * @returns {jsPDF}
  * @methodOf jsPDF#
  * @name addMetadata
  */

  (function (jsPDFAPI) {
      'use strict';

      var xmpmetadata = "";
      var xmpnamespaceuri = "";
      var metadata_object_number = "";

      jsPDFAPI.addMetadata = function (metadata, namespaceuri) {
          xmpnamespaceuri = namespaceuri || "http://jspdf.default.namespaceuri/"; //The namespace URI for an XMP name shall not be empty
          xmpmetadata = metadata;
          this.internal.events.subscribe('postPutResources', function () {
              if (!xmpmetadata) {
                  metadata_object_number = "";
              } else {
                  var xmpmeta_beginning = '<x:xmpmeta xmlns:x="adobe:ns:meta/">';
                  var rdf_beginning = '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#"><rdf:Description rdf:about="" xmlns:jspdf="' + xmpnamespaceuri + '"><jspdf:metadata>';
                  var rdf_ending = '</jspdf:metadata></rdf:Description></rdf:RDF>';
                  var xmpmeta_ending = '</x:xmpmeta>';
                  var utf8_xmpmeta_beginning = unescape(encodeURIComponent(xmpmeta_beginning));
                  var utf8_rdf_beginning = unescape(encodeURIComponent(rdf_beginning));
                  var utf8_metadata = unescape(encodeURIComponent(xmpmetadata));
                  var utf8_rdf_ending = unescape(encodeURIComponent(rdf_ending));
                  var utf8_xmpmeta_ending = unescape(encodeURIComponent(xmpmeta_ending));

                  var total_len = utf8_rdf_beginning.length + utf8_metadata.length + utf8_rdf_ending.length + utf8_xmpmeta_beginning.length + utf8_xmpmeta_ending.length;

                  metadata_object_number = this.internal.newObject();
                  this.internal.write('<< /Type /Metadata /Subtype /XML /Length ' + total_len + ' >>');
                  this.internal.write('stream');
                  this.internal.write(utf8_xmpmeta_beginning + utf8_rdf_beginning + utf8_metadata + utf8_rdf_ending + utf8_xmpmeta_ending);
                  this.internal.write('endstream');
                  this.internal.write('endobj');
              }
          });
          this.internal.events.subscribe('putCatalog', function () {
              if (metadata_object_number) {
                  this.internal.write('/Metadata ' + metadata_object_number + ' 0 R');
              }
          });
          return this;
      };
  })(jsPDF.API);

  /* Blob.js
   * A Blob implementation.
   * 2014-07-24
   *
   * By Eli Grey, http://eligrey.com
   * By Devin Samarin, https://github.com/dsamarin
   * License: X11/MIT
   *   See https://github.com/eligrey/Blob.js/blob/master/LICENSE.md
   */

  /*global self, unescape */
  /*jslint bitwise: true, regexp: true, confusion: true, es5: true, vars: true, white: true,
    plusplus: true */

  /*! @source http://purl.eligrey.com/github/Blob.js/blob/master/Blob.js */

  (function (view) {
  	"use strict";

  	view.URL = view.URL || view.webkitURL;

  	if (view.Blob && view.URL) {
  		try {
  			new Blob;
  			return;
  		} catch (e) {}
  	}

  	// Internally we use a BlobBuilder implementation to base Blob off of
  	// in order to support older browsers that only have BlobBuilder
  	var BlobBuilder = view.BlobBuilder || view.WebKitBlobBuilder || view.MozBlobBuilder || (function(view) {
  		var
  			  get_class = function(object) {
  				return Object.prototype.toString.call(object).match(/^\[object\s(.*)\]$/)[1];
  			}
  			, FakeBlobBuilder = function BlobBuilder() {
  				this.data = [];
  			}
  			, FakeBlob = function Blob(data, type, encoding) {
  				this.data = data;
  				this.size = data.length;
  				this.type = type;
  				this.encoding = encoding;
  			}
  			, FBB_proto = FakeBlobBuilder.prototype
  			, FB_proto = FakeBlob.prototype
  			, FileReaderSync = view.FileReaderSync
  			, FileException = function(type) {
  				this.code = this[this.name = type];
  			}
  			, file_ex_codes = (
  				  "NOT_FOUND_ERR SECURITY_ERR ABORT_ERR NOT_READABLE_ERR ENCODING_ERR "
  				+ "NO_MODIFICATION_ALLOWED_ERR INVALID_STATE_ERR SYNTAX_ERR"
  			).split(" ")
  			, file_ex_code = file_ex_codes.length
  			, real_URL = view.URL || view.webkitURL || view
  			, real_create_object_URL = real_URL.createObjectURL
  			, real_revoke_object_URL = real_URL.revokeObjectURL
  			, URL = real_URL
  			, btoa = view.btoa
  			, atob = view.atob

  			, ArrayBuffer = view.ArrayBuffer
  			, Uint8Array = view.Uint8Array

  			, origin = /^[\w-]+:\/*\[?[\w\.:-]+\]?(?::[0-9]+)?/
  		;
  		FakeBlob.fake = FB_proto.fake = true;
  		while (file_ex_code--) {
  			FileException.prototype[file_ex_codes[file_ex_code]] = file_ex_code + 1;
  		}
  		// Polyfill URL
  		if (!real_URL.createObjectURL) {
  			URL = view.URL = function(uri) {
  				var
  					  uri_info = document.createElementNS("http://www.w3.org/1999/xhtml", "a")
  					, uri_origin
  				;
  				uri_info.href = uri;
  				if (!("origin" in uri_info)) {
  					if (uri_info.protocol.toLowerCase() === "data:") {
  						uri_info.origin = null;
  					} else {
  						uri_origin = uri.match(origin);
  						uri_info.origin = uri_origin && uri_origin[1];
  					}
  				}
  				return uri_info;
  			};
  		}
  		URL.createObjectURL = function(blob) {
  			var
  				  type = blob.type
  				, data_URI_header
  			;
  			if (type === null) {
  				type = "application/octet-stream";
  			}
  			if (blob instanceof FakeBlob) {
  				data_URI_header = "data:" + type;
  				if (blob.encoding === "base64") {
  					return data_URI_header + ";base64," + blob.data;
  				} else if (blob.encoding === "URI") {
  					return data_URI_header + "," + decodeURIComponent(blob.data);
  				} if (btoa) {
  					return data_URI_header + ";base64," + btoa(blob.data);
  				} else {
  					return data_URI_header + "," + encodeURIComponent(blob.data);
  				}
  			} else if (real_create_object_URL) {
  				return real_create_object_URL.call(real_URL, blob);
  			}
  		};
  		URL.revokeObjectURL = function(object_URL) {
  			if (object_URL.substring(0, 5) !== "data:" && real_revoke_object_URL) {
  				real_revoke_object_URL.call(real_URL, object_URL);
  			}
  		};
  		FBB_proto.append = function(data/*, endings*/) {
  			var bb = this.data;
  			// decode data to a binary string
  			if (Uint8Array && (data instanceof ArrayBuffer || data instanceof Uint8Array)) {
  				var
  					  str = ""
  					, buf = new Uint8Array(data)
  					, i = 0
  					, buf_len = buf.length
  				;
  				for (; i < buf_len; i++) {
  					str += String.fromCharCode(buf[i]);
  				}
  				bb.push(str);
  			} else if (get_class(data) === "Blob" || get_class(data) === "File") {
  				if (FileReaderSync) {
  					var fr = new FileReaderSync;
  					bb.push(fr.readAsBinaryString(data));
  				} else {
  					// async FileReader won't work as BlobBuilder is sync
  					throw new FileException("NOT_READABLE_ERR");
  				}
  			} else if (data instanceof FakeBlob) {
  				if (data.encoding === "base64" && atob) {
  					bb.push(atob(data.data));
  				} else if (data.encoding === "URI") {
  					bb.push(decodeURIComponent(data.data));
  				} else if (data.encoding === "raw") {
  					bb.push(data.data);
  				}
  			} else {
  				if (typeof data !== "string") {
  					data += ""; // convert unsupported types to strings
  				}
  				// decode UTF-16 to binary string
  				bb.push(unescape(encodeURIComponent(data)));
  			}
  		};
  		FBB_proto.getBlob = function(type) {
  			if (!arguments.length) {
  				type = null;
  			}
  			return new FakeBlob(this.data.join(""), type, "raw");
  		};
  		FBB_proto.toString = function() {
  			return "[object BlobBuilder]";
  		};
  		FB_proto.slice = function(start, end, type) {
  			var args = arguments.length;
  			if (args < 3) {
  				type = null;
  			}
  			return new FakeBlob(
  				  this.data.slice(start, args > 1 ? end : this.data.length)
  				, type
  				, this.encoding
  			);
  		};
  		FB_proto.toString = function() {
  			return "[object Blob]";
  		};
  		FB_proto.close = function() {
  			this.size = 0;
  			delete this.data;
  		};
  		return FakeBlobBuilder;
  	}(view));

  	view.Blob = function(blobParts, options) {
  		var type = options ? (options.type || "") : "";
  		var builder = new BlobBuilder();
  		if (blobParts) {
  			for (var i = 0, len = blobParts.length; i < len; i++) {
  				if (Uint8Array && blobParts[i] instanceof Uint8Array) {
  					builder.append(blobParts[i].buffer);
  				}
  				else {
  					builder.append(blobParts[i]);
  				}
  			}
  		}
  		var blob = builder.getBlob(type);
  		if (!blob.slice && blob.webkitSlice) {
  			blob.slice = blob.webkitSlice;
  		}
  		return blob;
  	};

  	var getPrototypeOf = Object.getPrototypeOf || function(object) {
  		return object.__proto__;
  	};
  	view.Blob.prototype = getPrototypeOf(new view.Blob());
  }(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined.content || undefined));

  /* FileSaver.js
   * A saveAs() FileSaver implementation.
   * 1.1.20151003
   *
   * By Eli Grey, http://eligrey.com
   * License: MIT
   *   See https://github.com/eligrey/FileSaver.js/blob/master/LICENSE.md
   */

  /*global self */
  /*jslint bitwise: true, indent: 4, laxbreak: true, laxcomma: true, smarttabs: true, plusplus: true */

  /*! @source http://purl.eligrey.com/github/FileSaver.js/blob/master/FileSaver.js */

  var saveAs = saveAs || (function(view) {
  	"use strict";
  	// IE <10 is explicitly unsupported
  	if (typeof navigator !== "undefined" && /MSIE [1-9]\./.test(navigator.userAgent)) {
  		return;
  	}
  	var
  		  doc = view.document
  		  // only get URL when necessary in case Blob.js hasn't overridden it yet
  		, get_URL = function() {
  			return view.URL || view.webkitURL || view;
  		}
  		, save_link = doc.createElementNS("http://www.w3.org/1999/xhtml", "a")
  		, can_use_save_link = "download" in save_link
  		, click = function(node) {
  			var event = new MouseEvent("click");
  			node.dispatchEvent(event);
  		}
  		, is_safari = /Version\/[\d\.]+.*Safari/.test(navigator.userAgent)
  		, webkit_req_fs = view.webkitRequestFileSystem
  		, req_fs = view.requestFileSystem || webkit_req_fs || view.mozRequestFileSystem
  		, throw_outside = function(ex) {
  			(view.setImmediate || view.setTimeout)(function() {
  				throw ex;
  			}, 0);
  		}
  		, force_saveable_type = "application/octet-stream"
  		, fs_min_size = 0
  		// See https://code.google.com/p/chromium/issues/detail?id=375297#c7 and
  		// https://github.com/eligrey/FileSaver.js/commit/485930a#commitcomment-8768047
  		// for the reasoning behind the timeout and revocation flow
  		, arbitrary_revoke_timeout = 500 // in ms
  		, revoke = function(file) {
  			var revoker = function() {
  				if (typeof file === "string") { // file is an object URL
  					get_URL().revokeObjectURL(file);
  				} else { // file is a File
  					file.remove();
  				}
  			};
  			if (view.chrome) {
  				revoker();
  			} else {
  				setTimeout(revoker, arbitrary_revoke_timeout);
  			}
  		}
  		, dispatch = function(filesaver, event_types, event) {
  			event_types = [].concat(event_types);
  			var i = event_types.length;
  			while (i--) {
  				var listener = filesaver["on" + event_types[i]];
  				if (typeof listener === "function") {
  					try {
  						listener.call(filesaver, event || filesaver);
  					} catch (ex) {
  						throw_outside(ex);
  					}
  				}
  			}
  		}
  		, auto_bom = function(blob) {
  			// prepend BOM for UTF-8 XML and text/* types (including HTML)
  			if (/^\s*(?:text\/\S*|application\/xml|\S*\/\S*\+xml)\s*;.*charset\s*=\s*utf-8/i.test(blob.type)) {
  				return new Blob(["\ufeff", blob], {type: blob.type});
  			}
  			return blob;
  		}
  		, FileSaver = function(blob, name, no_auto_bom) {
  			if (!no_auto_bom) {
  				blob = auto_bom(blob);
  			}
  			// First try a.download, then web filesystem, then object URLs
  			var
  				  filesaver = this
  				, type = blob.type
  				, blob_changed = false
  				, object_url
  				, target_view
  				, dispatch_all = function() {
  					dispatch(filesaver, "writestart progress write writeend".split(" "));
  				}
  				// on any filesys errors revert to saving with object URLs
  				, fs_error = function() {
  					if (target_view && is_safari && typeof FileReader !== "undefined") {
  						// Safari doesn't allow downloading of blob urls
  						var reader = new FileReader();
  						reader.onloadend = function() {
  							var base64Data = reader.result;
  							target_view.location.href = "data:attachment/file" + base64Data.slice(base64Data.search(/[,;]/));
  							filesaver.readyState = filesaver.DONE;
  							dispatch_all();
  						};
  						reader.readAsDataURL(blob);
  						filesaver.readyState = filesaver.INIT;
  						return;
  					}
  					// don't create more object URLs than needed
  					if (blob_changed || !object_url) {
  						object_url = get_URL().createObjectURL(blob);
  					}
  					if (target_view) {
  						target_view.location.href = object_url;
  					} else {
  						var new_tab = view.open(object_url, "_blank");
  						if (new_tab == undefined && is_safari) {
  							//Apple do not allow window.open, see http://bit.ly/1kZffRI
  							view.location.href = object_url
  						}
  					}
  					filesaver.readyState = filesaver.DONE;
  					dispatch_all();
  					revoke(object_url);
  				}
  				, abortable = function(func) {
  					return function() {
  						if (filesaver.readyState !== filesaver.DONE) {
  							return func.apply(this, arguments);
  						}
  					};
  				}
  				, create_if_not_found = {create: true, exclusive: false}
  				, slice
  			;
  			filesaver.readyState = filesaver.INIT;
  			if (!name) {
  				name = "download";
  			}
  			if (can_use_save_link) {
  				object_url = get_URL().createObjectURL(blob);
  				setTimeout(function() {
  					save_link.href = object_url;
  					save_link.download = name;
  					click(save_link);
  					dispatch_all();
  					revoke(object_url);
  					filesaver.readyState = filesaver.DONE;
  				});
  				return;
  			}
  			// Object and web filesystem URLs have a problem saving in Google Chrome when
  			// viewed in a tab, so I force save with application/octet-stream
  			// http://code.google.com/p/chromium/issues/detail?id=91158
  			// Update: Google errantly closed 91158, I submitted it again:
  			// https://code.google.com/p/chromium/issues/detail?id=389642
  			if (view.chrome && type && type !== force_saveable_type) {
  				slice = blob.slice || blob.webkitSlice;
  				blob = slice.call(blob, 0, blob.size, force_saveable_type);
  				blob_changed = true;
  			}
  			// Since I can't be sure that the guessed media type will trigger a download
  			// in WebKit, I append .download to the filename.
  			// https://bugs.webkit.org/show_bug.cgi?id=65440
  			if (webkit_req_fs && name !== "download") {
  				name += ".download";
  			}
  			if (type === force_saveable_type || webkit_req_fs) {
  				target_view = view;
  			}
  			if (!req_fs) {
  				fs_error();
  				return;
  			}
  			fs_min_size += blob.size;
  			req_fs(view.TEMPORARY, fs_min_size, abortable(function(fs) {
  				fs.root.getDirectory("saved", create_if_not_found, abortable(function(dir) {
  					var save = function() {
  						dir.getFile(name, create_if_not_found, abortable(function(file) {
  							file.createWriter(abortable(function(writer) {
  								writer.onwriteend = function(event) {
  									target_view.location.href = file.toURL();
  									filesaver.readyState = filesaver.DONE;
  									dispatch(filesaver, "writeend", event);
  									revoke(file);
  								};
  								writer.onerror = function() {
  									var error = writer.error;
  									if (error.code !== error.ABORT_ERR) {
  										fs_error();
  									}
  								};
  								"writestart progress write abort".split(" ").forEach(function(event) {
  									writer["on" + event] = filesaver["on" + event];
  								});
  								writer.write(blob);
  								filesaver.abort = function() {
  									writer.abort();
  									filesaver.readyState = filesaver.DONE;
  								};
  								filesaver.readyState = filesaver.WRITING;
  							}), fs_error);
  						}), fs_error);
  					};
  					dir.getFile(name, {create: false}, abortable(function(file) {
  						// delete file if it already exists
  						file.remove();
  						save();
  					}), abortable(function(ex) {
  						if (ex.code === ex.NOT_FOUND_ERR) {
  							save();
  						} else {
  							fs_error();
  						}
  					}));
  				}), fs_error);
  			}), fs_error);
  		}
  		, FS_proto = FileSaver.prototype
  		, saveAs = function(blob, name, no_auto_bom) {
  			return new FileSaver(blob, name, no_auto_bom);
  		}
  	;
  	// IE 10+ (native saveAs)
  	if (typeof navigator !== "undefined" && navigator.msSaveOrOpenBlob) {
  		return function(blob, name, no_auto_bom) {
  			if (!no_auto_bom) {
  				blob = auto_bom(blob);
  			}
  			return navigator.msSaveOrOpenBlob(blob, name || "download");
  		};
  	}

  	FS_proto.abort = function() {
  		var filesaver = this;
  		filesaver.readyState = filesaver.DONE;
  		dispatch(filesaver, "abort");
  	};
  	FS_proto.readyState = FS_proto.INIT = 0;
  	FS_proto.WRITING = 1;
  	FS_proto.DONE = 2;

  	FS_proto.error =
  	FS_proto.onwritestart =
  	FS_proto.onprogress =
  	FS_proto.onwrite =
  	FS_proto.onabort =
  	FS_proto.onerror =
  	FS_proto.onwriteend =
  		null;

  	return saveAs;
  }(
  	   typeof self !== "undefined" && self
  	|| typeof window !== "undefined" && window
  	|| undefined.content
  ));
  // `self` is undefined in Firefox for Android content script context
  // while `this` is nsIContentFrameMessageManager
  // with an attribute `content` that corresponds to the window

  if (typeof module !== "undefined" && module.exports) {
    module.exports.saveAs = saveAs;
  } else if ((typeof define !== "undefined" && define !== null) && (define.amd != null)) {
    define([], function() {
      return saveAs;
    });
  }

  /*
   * Copyright (c) 2012 chick307 <chick307@gmail.com>
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  void function(global, callback) {
  	if (typeof module === 'object') {
  		module.exports = callback();
  	} else if (typeof define === 'function') {
  		define(callback);
  	} else {
  		global.adler32cs = callback();
  	}
  }(jsPDF, function() {
  	var _hasArrayBuffer = typeof ArrayBuffer === 'function' &&
  		typeof Uint8Array === 'function';

  	var _Buffer = null, _isBuffer = (function() {
  		if (!_hasArrayBuffer)
  			return function _isBuffer() { return false };

  		try {
  			var buffer = {};
  			if (typeof buffer.Buffer === 'function')
  				_Buffer = buffer.Buffer;
  		} catch (error) {}

  		return function _isBuffer(value) {
  			return value instanceof ArrayBuffer ||
  				_Buffer !== null && value instanceof _Buffer;
  		};
  	}());

  	var _utf8ToBinary = (function() {
  		if (_Buffer !== null) {
  			return function _utf8ToBinary(utf8String) {
  				return new _Buffer(utf8String, 'utf8').toString('binary');
  			};
  		} else {
  			return function _utf8ToBinary(utf8String) {
  				return unescape(encodeURIComponent(utf8String));
  			};
  		}
  	}());

  	var MOD = 65521;

  	var _update = function _update(checksum, binaryString) {
  		var a = checksum & 0xFFFF, b = checksum >>> 16;
  		for (var i = 0, length = binaryString.length; i < length; i++) {
  			a = (a + (binaryString.charCodeAt(i) & 0xFF)) % MOD;
  			b = (b + a) % MOD;
  		}
  		return (b << 16 | a) >>> 0;
  	};

  	var _updateUint8Array = function _updateUint8Array(checksum, uint8Array) {
  		var a = checksum & 0xFFFF, b = checksum >>> 16;
  		for (var i = 0, length = uint8Array.length, x; i < length; i++) {
  			a = (a + uint8Array[i]) % MOD;
  			b = (b + a) % MOD;
  		}
  		return (b << 16 | a) >>> 0
  	};

  	var exports = {};

  	var Adler32 = exports.Adler32 = (function() {
  		var ctor = function Adler32(checksum) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (!isFinite(checksum = checksum == null ? 1 : +checksum)) {
  				throw new Error(
  					'First arguments needs to be a finite number.');
  			}
  			this.checksum = checksum >>> 0;
  		};

  		var proto = ctor.prototype = {};
  		proto.constructor = ctor;

  		ctor.from = function(from) {
  			from.prototype = proto;
  			return from;
  		}(function from(binaryString) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (binaryString == null)
  				throw new Error('First argument needs to be a string.');
  			this.checksum = _update(1, binaryString.toString());
  		});

  		ctor.fromUtf8 = function(fromUtf8) {
  			fromUtf8.prototype = proto;
  			return fromUtf8;
  		}(function fromUtf8(utf8String) {
  			if (!(this instanceof ctor)) {
  				throw new TypeError(
  					'Constructor cannot called be as a function.');
  			}
  			if (utf8String == null)
  				throw new Error('First argument needs to be a string.');
  			var binaryString = _utf8ToBinary(utf8String.toString());
  			this.checksum = _update(1, binaryString);
  		});

  		if (_hasArrayBuffer) {
  			ctor.fromBuffer = function(fromBuffer) {
  				fromBuffer.prototype = proto;
  				return fromBuffer;
  			}(function fromBuffer(buffer) {
  				if (!(this instanceof ctor)) {
  					throw new TypeError(
  						'Constructor cannot called be as a function.');
  				}
  				if (!_isBuffer(buffer))
  					throw new Error('First argument needs to be ArrayBuffer.');
  				var array = new Uint8Array(buffer);
  				return this.checksum = _updateUint8Array(1, array);
  			});
  		}

  		proto.update = function update(binaryString) {
  			if (binaryString == null)
  				throw new Error('First argument needs to be a string.');
  			binaryString = binaryString.toString();
  			return this.checksum = _update(this.checksum, binaryString);
  		};

  		proto.updateUtf8 = function updateUtf8(utf8String) {
  			if (utf8String == null)
  				throw new Error('First argument needs to be a string.');
  			var binaryString = _utf8ToBinary(utf8String.toString());
  			return this.checksum = _update(this.checksum, binaryString);
  		};

  		if (_hasArrayBuffer) {
  			proto.updateBuffer = function updateBuffer(buffer) {
  				if (!_isBuffer(buffer))
  					throw new Error('First argument needs to be ArrayBuffer.');
  				var array = new Uint8Array(buffer);
  				return this.checksum = _updateUint8Array(this.checksum, array);
  			};
  		}

  		proto.clone = function clone() {
  			return new Adler32(this.checksum);
  		};

  		return ctor;
  	}());

  	exports.from = function from(binaryString) {
  		if (binaryString == null)
  			throw new Error('First argument needs to be a string.');
  		return _update(1, binaryString.toString());
  	};

  	exports.fromUtf8 = function fromUtf8(utf8String) {
  		if (utf8String == null)
  			throw new Error('First argument needs to be a string.');
  		var binaryString = _utf8ToBinary(utf8String.toString());
  		return _update(1, binaryString);
  	};

  	if (_hasArrayBuffer) {
  		exports.fromBuffer = function fromBuffer(buffer) {
  			if (!_isBuffer(buffer))
  				throw new Error('First argument need to be ArrayBuffer.');
  			var array = new Uint8Array(buffer);
  			return _updateUint8Array(1, array);
  		};
  	}

  	return exports;
  });

  /**
   * CssColors
   * Copyright (c) 2014 Steven Spungin (TwelveTone LLC)  steven@twelvetone.tv
   *
   * Licensed under the MIT License.
   * http://opensource.org/licenses/mit-license
   */

  /**
   * Usage CssColors('red');
   * Returns RGB hex color with '#' prefix
   */

  var CssColors = {};
  CssColors._colorsTable = {
  	"aliceblue" : "#f0f8ff",
  	"antiquewhite" : "#faebd7",
  	"aqua" : "#00ffff",
  	"aquamarine" : "#7fffd4",
  	"azure" : "#f0ffff",
  	"beige" : "#f5f5dc",
  	"bisque" : "#ffe4c4",
  	"black" : "#000000",
  	"blanchedalmond" : "#ffebcd",
  	"blue" : "#0000ff",
  	"blueviolet" : "#8a2be2",
  	"brown" : "#a52a2a",
  	"burlywood" : "#deb887",
  	"cadetblue" : "#5f9ea0",
  	"chartreuse" : "#7fff00",
  	"chocolate" : "#d2691e",
  	"coral" : "#ff7f50",
  	"cornflowerblue" : "#6495ed",
  	"cornsilk" : "#fff8dc",
  	"crimson" : "#dc143c",
  	"cyan" : "#00ffff",
  	"darkblue" : "#00008b",
  	"darkcyan" : "#008b8b",
  	"darkgoldenrod" : "#b8860b",
  	"darkgray" : "#a9a9a9",
  	"darkgreen" : "#006400",
  	"darkkhaki" : "#bdb76b",
  	"darkmagenta" : "#8b008b",
  	"darkolivegreen" : "#556b2f",
  	"darkorange" : "#ff8c00",
  	"darkorchid" : "#9932cc",
  	"darkred" : "#8b0000",
  	"darksalmon" : "#e9967a",
  	"darkseagreen" : "#8fbc8f",
  	"darkslateblue" : "#483d8b",
  	"darkslategray" : "#2f4f4f",
  	"darkturquoise" : "#00ced1",
  	"darkviolet" : "#9400d3",
  	"deeppink" : "#ff1493",
  	"deepskyblue" : "#00bfff",
  	"dimgray" : "#696969",
  	"dodgerblue" : "#1e90ff",
  	"firebrick" : "#b22222",
  	"floralwhite" : "#fffaf0",
  	"forestgreen" : "#228b22",
  	"fuchsia" : "#ff00ff",
  	"gainsboro" : "#dcdcdc",
  	"ghostwhite" : "#f8f8ff",
  	"gold" : "#ffd700",
  	"goldenrod" : "#daa520",
  	"gray" : "#808080",
  	"green" : "#008000",
  	"greenyellow" : "#adff2f",
  	"honeydew" : "#f0fff0",
  	"hotpink" : "#ff69b4",
  	"indianred " : "#cd5c5c",
  	"indigo" : "#4b0082",
  	"ivory" : "#fffff0",
  	"khaki" : "#f0e68c",
  	"lavender" : "#e6e6fa",
  	"lavenderblush" : "#fff0f5",
  	"lawngreen" : "#7cfc00",
  	"lemonchiffon" : "#fffacd",
  	"lightblue" : "#add8e6",
  	"lightcoral" : "#f08080",
  	"lightcyan" : "#e0ffff",
  	"lightgoldenrodyellow" : "#fafad2",
  	"lightgrey" : "#d3d3d3",
  	"lightgreen" : "#90ee90",
  	"lightpink" : "#ffb6c1",
  	"lightsalmon" : "#ffa07a",
  	"lightseagreen" : "#20b2aa",
  	"lightskyblue" : "#87cefa",
  	"lightslategray" : "#778899",
  	"lightsteelblue" : "#b0c4de",
  	"lightyellow" : "#ffffe0",
  	"lime" : "#00ff00",
  	"limegreen" : "#32cd32",
  	"linen" : "#faf0e6",
  	"magenta" : "#ff00ff",
  	"maroon" : "#800000",
  	"mediumaquamarine" : "#66cdaa",
  	"mediumblue" : "#0000cd",
  	"mediumorchid" : "#ba55d3",
  	"mediumpurple" : "#9370d8",
  	"mediumseagreen" : "#3cb371",
  	"mediumslateblue" : "#7b68ee",
  	"mediumspringgreen" : "#00fa9a",
  	"mediumturquoise" : "#48d1cc",
  	"mediumvioletred" : "#c71585",
  	"midnightblue" : "#191970",
  	"mintcream" : "#f5fffa",
  	"mistyrose" : "#ffe4e1",
  	"moccasin" : "#ffe4b5",
  	"navajowhite" : "#ffdead",
  	"navy" : "#000080",
  	"oldlace" : "#fdf5e6",
  	"olive" : "#808000",
  	"olivedrab" : "#6b8e23",
  	"orange" : "#ffa500",
  	"orangered" : "#ff4500",
  	"orchid" : "#da70d6",
  	"palegoldenrod" : "#eee8aa",
  	"palegreen" : "#98fb98",
  	"paleturquoise" : "#afeeee",
  	"palevioletred" : "#d87093",
  	"papayawhip" : "#ffefd5",
  	"peachpuff" : "#ffdab9",
  	"peru" : "#cd853f",
  	"pink" : "#ffc0cb",
  	"plum" : "#dda0dd",
  	"powderblue" : "#b0e0e6",
  	"purple" : "#800080",
  	"red" : "#ff0000",
  	"rosybrown" : "#bc8f8f",
  	"royalblue" : "#4169e1",
  	"saddlebrown" : "#8b4513",
  	"salmon" : "#fa8072",
  	"sandybrown" : "#f4a460",
  	"seagreen" : "#2e8b57",
  	"seashell" : "#fff5ee",
  	"sienna" : "#a0522d",
  	"silver" : "#c0c0c0",
  	"skyblue" : "#87ceeb",
  	"slateblue" : "#6a5acd",
  	"slategray" : "#708090",
  	"snow" : "#fffafa",
  	"springgreen" : "#00ff7f",
  	"steelblue" : "#4682b4",
  	"tan" : "#d2b48c",
  	"teal" : "#008080",
  	"thistle" : "#d8bfd8",
  	"tomato" : "#ff6347",
  	"turquoise" : "#40e0d0",
  	"violet" : "#ee82ee",
  	"wheat" : "#f5deb3",
  	"white" : "#ffffff",
  	"whitesmoke" : "#f5f5f5",
  	"yellow" : "#ffff00",
  	"yellowgreen" : "#9acd32"
  };

  CssColors.colorNameToHex = function(color) {
  	color = color.toLowerCase();
  	if (typeof this._colorsTable[color] != 'undefined')
  		return this._colorsTable[color];

  	return false;
  };

  /*
   Deflate.js - https://github.com/gildas-lormeau/zip.js
   Copyright (c) 2013 Gildas Lormeau. All rights reserved.

   Redistribution and use in source and binary forms, with or without
   modification, are permitted provided that the following conditions are met:

   1. Redistributions of source code must retain the above copyright notice,
   this list of conditions and the following disclaimer.

   2. Redistributions in binary form must reproduce the above copyright 
   notice, this list of conditions and the following disclaimer in 
   the documentation and/or other materials provided with the distribution.

   3. The names of the authors may not be used to endorse or promote products
   derived from this software without specific prior written permission.

   THIS SOFTWARE IS PROVIDED ``AS IS'' AND ANY EXPRESSED OR IMPLIED WARRANTIES,
   INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
   FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL JCRAFT,
   INC. OR ANY CONTRIBUTORS TO THIS SOFTWARE BE LIABLE FOR ANY DIRECT, INDIRECT,
   INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
   LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA,
   OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
   LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
   NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
   EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
   */

  /*
   * This program is based on JZlib 1.0.2 ymnk, JCraft,Inc.
   * JZlib is based on zlib-1.1.3, so all credit should go authors
   * Jean-loup Gailly(jloup@gzip.org) and Mark Adler(madler@alumni.caltech.edu)
   * and contributors of zlib.
   */

  var Deflater = (function(obj) {

  	// Global

  	var MAX_BITS = 15;
  	var D_CODES = 30;
  	var BL_CODES = 19;

  	var LENGTH_CODES = 29;
  	var LITERALS = 256;
  	var L_CODES = (LITERALS + 1 + LENGTH_CODES);
  	var HEAP_SIZE = (2 * L_CODES + 1);

  	var END_BLOCK = 256;

  	// Bit length codes must not exceed MAX_BL_BITS bits
  	var MAX_BL_BITS = 7;

  	// repeat previous bit length 3-6 times (2 bits of repeat count)
  	var REP_3_6 = 16;

  	// repeat a zero length 3-10 times (3 bits of repeat count)
  	var REPZ_3_10 = 17;

  	// repeat a zero length 11-138 times (7 bits of repeat count)
  	var REPZ_11_138 = 18;

  	// The lengths of the bit length codes are sent in order of decreasing
  	// probability, to avoid transmitting the lengths for unused bit
  	// length codes.

  	var Buf_size = 8 * 2;

  	// JZlib version : "1.0.2"
  	var Z_DEFAULT_COMPRESSION = -1;

  	// compression strategy
  	var Z_FILTERED = 1;
  	var Z_HUFFMAN_ONLY = 2;
  	var Z_DEFAULT_STRATEGY = 0;

  	var Z_NO_FLUSH = 0;
  	var Z_PARTIAL_FLUSH = 1;
  	var Z_FULL_FLUSH = 3;
  	var Z_FINISH = 4;

  	var Z_OK = 0;
  	var Z_STREAM_END = 1;
  	var Z_NEED_DICT = 2;
  	var Z_STREAM_ERROR = -2;
  	var Z_DATA_ERROR = -3;
  	var Z_BUF_ERROR = -5;

  	// Tree

  	// see definition of array dist_code below
  	var _dist_code = [ 0, 1, 2, 3, 4, 4, 5, 5, 6, 6, 6, 6, 7, 7, 7, 7, 8, 8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9, 9, 9, 9, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
  			10, 10, 10, 10, 10, 10, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 11, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12,
  			12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13,
  			13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14,
  			14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14, 14,
  			14, 14, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15,
  			15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 15, 0, 0, 16, 17, 18, 18, 19, 19,
  			20, 20, 20, 20, 21, 21, 21, 21, 22, 22, 22, 22, 22, 22, 22, 22, 23, 23, 23, 23, 23, 23, 23, 23, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  			24, 24, 24, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26,
  			26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27,
  			27, 27, 27, 27, 27, 27, 27, 27, 27, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
  			28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 29,
  			29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29,
  			29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29, 29 ];

  	function Tree() {
  		var that = this;

  		// dyn_tree; // the dynamic tree
  		// max_code; // largest code with non zero frequency
  		// stat_desc; // the corresponding static tree

  		// Compute the optimal bit lengths for a tree and update the total bit
  		// length
  		// for the current block.
  		// IN assertion: the fields freq and dad are set, heap[heap_max] and
  		// above are the tree nodes sorted by increasing frequency.
  		// OUT assertions: the field len is set to the optimal bit length, the
  		// array bl_count contains the frequencies for each bit length.
  		// The length opt_len is updated; static_len is also updated if stree is
  		// not null.
  		function gen_bitlen(s) {
  			var tree = that.dyn_tree;
  			var stree = that.stat_desc.static_tree;
  			var extra = that.stat_desc.extra_bits;
  			var base = that.stat_desc.extra_base;
  			var max_length = that.stat_desc.max_length;
  			var h; // heap index
  			var n, m; // iterate over the tree elements
  			var bits; // bit length
  			var xbits; // extra bits
  			var f; // frequency
  			var overflow = 0; // number of elements with bit length too large

  			for (bits = 0; bits <= MAX_BITS; bits++)
  				s.bl_count[bits] = 0;

  			// In a first pass, compute the optimal bit lengths (which may
  			// overflow in the case of the bit length tree).
  			tree[s.heap[s.heap_max] * 2 + 1] = 0; // root of the heap

  			for (h = s.heap_max + 1; h < HEAP_SIZE; h++) {
  				n = s.heap[h];
  				bits = tree[tree[n * 2 + 1] * 2 + 1] + 1;
  				if (bits > max_length) {
  					bits = max_length;
  					overflow++;
  				}
  				tree[n * 2 + 1] = bits;
  				// We overwrite tree[n*2+1] which is no longer needed

  				if (n > that.max_code)
  					continue; // not a leaf node

  				s.bl_count[bits]++;
  				xbits = 0;
  				if (n >= base)
  					xbits = extra[n - base];
  				f = tree[n * 2];
  				s.opt_len += f * (bits + xbits);
  				if (stree)
  					s.static_len += f * (stree[n * 2 + 1] + xbits);
  			}
  			if (overflow === 0)
  				return;

  			// This happens for example on obj2 and pic of the Calgary corpus
  			// Find the first bit length which could increase:
  			do {
  				bits = max_length - 1;
  				while (s.bl_count[bits] === 0)
  					bits--;
  				s.bl_count[bits]--; // move one leaf down the tree
  				s.bl_count[bits + 1] += 2; // move one overflow item as its brother
  				s.bl_count[max_length]--;
  				// The brother of the overflow item also moves one step up,
  				// but this does not affect bl_count[max_length]
  				overflow -= 2;
  			} while (overflow > 0);

  			for (bits = max_length; bits !== 0; bits--) {
  				n = s.bl_count[bits];
  				while (n !== 0) {
  					m = s.heap[--h];
  					if (m > that.max_code)
  						continue;
  					if (tree[m * 2 + 1] != bits) {
  						s.opt_len += (bits - tree[m * 2 + 1]) * tree[m * 2];
  						tree[m * 2 + 1] = bits;
  					}
  					n--;
  				}
  			}
  		}

  		// Reverse the first len bits of a code, using straightforward code (a
  		// faster
  		// method would use a table)
  		// IN assertion: 1 <= len <= 15
  		function bi_reverse(code, // the value to invert
  		len // its bit length
  		) {
  			var res = 0;
  			do {
  				res |= code & 1;
  				code >>>= 1;
  				res <<= 1;
  			} while (--len > 0);
  			return res >>> 1;
  		}

  		// Generate the codes for a given tree and bit counts (which need not be
  		// optimal).
  		// IN assertion: the array bl_count contains the bit length statistics for
  		// the given tree and the field len is set for all tree elements.
  		// OUT assertion: the field code is set for all tree elements of non
  		// zero code length.
  		function gen_codes(tree, // the tree to decorate
  		max_code, // largest code with non zero frequency
  		bl_count // number of codes at each bit length
  		) {
  			var next_code = []; // next code value for each
  			// bit length
  			var code = 0; // running code value
  			var bits; // bit index
  			var n; // code index
  			var len;

  			// The distribution counts are first used to generate the code values
  			// without bit reversal.
  			for (bits = 1; bits <= MAX_BITS; bits++) {
  				next_code[bits] = code = ((code + bl_count[bits - 1]) << 1);
  			}

  			// Check that the bit counts in bl_count are consistent. The last code
  			// must be all ones.
  			// Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
  			// "inconsistent bit counts");
  			// Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

  			for (n = 0; n <= max_code; n++) {
  				len = tree[n * 2 + 1];
  				if (len === 0)
  					continue;
  				// Now reverse the bits
  				tree[n * 2] = bi_reverse(next_code[len]++, len);
  			}
  		}

  		// Construct one Huffman tree and assigns the code bit strings and lengths.
  		// Update the total bit length for the current block.
  		// IN assertion: the field freq is set for all tree elements.
  		// OUT assertions: the fields len and code are set to the optimal bit length
  		// and corresponding code. The length opt_len is updated; static_len is
  		// also updated if stree is not null. The field max_code is set.
  		that.build_tree = function(s) {
  			var tree = that.dyn_tree;
  			var stree = that.stat_desc.static_tree;
  			var elems = that.stat_desc.elems;
  			var n, m; // iterate over heap elements
  			var max_code = -1; // largest code with non zero frequency
  			var node; // new node being created

  			// Construct the initial heap, with least frequent element in
  			// heap[1]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
  			// heap[0] is not used.
  			s.heap_len = 0;
  			s.heap_max = HEAP_SIZE;

  			for (n = 0; n < elems; n++) {
  				if (tree[n * 2] !== 0) {
  					s.heap[++s.heap_len] = max_code = n;
  					s.depth[n] = 0;
  				} else {
  					tree[n * 2 + 1] = 0;
  				}
  			}

  			// The pkzip format requires that at least one distance code exists,
  			// and that at least one bit should be sent even if there is only one
  			// possible code. So to avoid special checks later on we force at least
  			// two codes of non zero frequency.
  			while (s.heap_len < 2) {
  				node = s.heap[++s.heap_len] = max_code < 2 ? ++max_code : 0;
  				tree[node * 2] = 1;
  				s.depth[node] = 0;
  				s.opt_len--;
  				if (stree)
  					s.static_len -= stree[node * 2 + 1];
  				// node is 0 or 1 so it does not have extra bits
  			}
  			that.max_code = max_code;

  			// The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
  			// establish sub-heaps of increasing lengths:

  			for (n = Math.floor(s.heap_len / 2); n >= 1; n--)
  				s.pqdownheap(tree, n);

  			// Construct the Huffman tree by repeatedly combining the least two
  			// frequent nodes.

  			node = elems; // next internal node of the tree
  			do {
  				// n = node of least frequency
  				n = s.heap[1];
  				s.heap[1] = s.heap[s.heap_len--];
  				s.pqdownheap(tree, 1);
  				m = s.heap[1]; // m = node of next least frequency

  				s.heap[--s.heap_max] = n; // keep the nodes sorted by frequency
  				s.heap[--s.heap_max] = m;

  				// Create a new node father of n and m
  				tree[node * 2] = (tree[n * 2] + tree[m * 2]);
  				s.depth[node] = Math.max(s.depth[n], s.depth[m]) + 1;
  				tree[n * 2 + 1] = tree[m * 2 + 1] = node;

  				// and insert the new node in the heap
  				s.heap[1] = node++;
  				s.pqdownheap(tree, 1);
  			} while (s.heap_len >= 2);

  			s.heap[--s.heap_max] = s.heap[1];

  			// At this point, the fields freq and dad are set. We can now
  			// generate the bit lengths.

  			gen_bitlen(s);

  			// The field len is now set, we can generate the bit codes
  			gen_codes(tree, that.max_code, s.bl_count);
  		};

  	}

  	Tree._length_code = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 15, 16, 16, 16, 16,
  			16, 16, 16, 16, 17, 17, 17, 17, 17, 17, 17, 17, 18, 18, 18, 18, 18, 18, 18, 18, 19, 19, 19, 19, 19, 19, 19, 19, 20, 20, 20, 20, 20, 20, 20, 20, 20,
  			20, 20, 20, 20, 20, 20, 20, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 21, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22, 22,
  			22, 22, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 23, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24,
  			24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 24, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25,
  			25, 25, 25, 25, 25, 25, 25, 25, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26, 26,
  			26, 26, 26, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 27, 28 ];

  	Tree.base_length = [ 0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 0 ];

  	Tree.base_dist = [ 0, 1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64, 96, 128, 192, 256, 384, 512, 768, 1024, 1536, 2048, 3072, 4096, 6144, 8192, 12288, 16384,
  			24576 ];

  	// Mapping from a distance to a distance code. dist is the distance - 1 and
  	// must not have side effects. _dist_code[256] and _dist_code[257] are never
  	// used.
  	Tree.d_code = function(dist) {
  		return ((dist) < 256 ? _dist_code[dist] : _dist_code[256 + ((dist) >>> 7)]);
  	};

  	// extra bits for each length code
  	Tree.extra_lbits = [ 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0 ];

  	// extra bits for each distance code
  	Tree.extra_dbits = [ 0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13 ];

  	// extra bits for each bit length code
  	Tree.extra_blbits = [ 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 3, 7 ];

  	Tree.bl_order = [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];

  	// StaticTree

  	function StaticTree(static_tree, extra_bits, extra_base, elems, max_length) {
  		var that = this;
  		that.static_tree = static_tree;
  		that.extra_bits = extra_bits;
  		that.extra_base = extra_base;
  		that.elems = elems;
  		that.max_length = max_length;
  	}

  	StaticTree.static_ltree = [ 12, 8, 140, 8, 76, 8, 204, 8, 44, 8, 172, 8, 108, 8, 236, 8, 28, 8, 156, 8, 92, 8, 220, 8, 60, 8, 188, 8, 124, 8, 252, 8, 2, 8,
  			130, 8, 66, 8, 194, 8, 34, 8, 162, 8, 98, 8, 226, 8, 18, 8, 146, 8, 82, 8, 210, 8, 50, 8, 178, 8, 114, 8, 242, 8, 10, 8, 138, 8, 74, 8, 202, 8, 42,
  			8, 170, 8, 106, 8, 234, 8, 26, 8, 154, 8, 90, 8, 218, 8, 58, 8, 186, 8, 122, 8, 250, 8, 6, 8, 134, 8, 70, 8, 198, 8, 38, 8, 166, 8, 102, 8, 230, 8,
  			22, 8, 150, 8, 86, 8, 214, 8, 54, 8, 182, 8, 118, 8, 246, 8, 14, 8, 142, 8, 78, 8, 206, 8, 46, 8, 174, 8, 110, 8, 238, 8, 30, 8, 158, 8, 94, 8,
  			222, 8, 62, 8, 190, 8, 126, 8, 254, 8, 1, 8, 129, 8, 65, 8, 193, 8, 33, 8, 161, 8, 97, 8, 225, 8, 17, 8, 145, 8, 81, 8, 209, 8, 49, 8, 177, 8, 113,
  			8, 241, 8, 9, 8, 137, 8, 73, 8, 201, 8, 41, 8, 169, 8, 105, 8, 233, 8, 25, 8, 153, 8, 89, 8, 217, 8, 57, 8, 185, 8, 121, 8, 249, 8, 5, 8, 133, 8,
  			69, 8, 197, 8, 37, 8, 165, 8, 101, 8, 229, 8, 21, 8, 149, 8, 85, 8, 213, 8, 53, 8, 181, 8, 117, 8, 245, 8, 13, 8, 141, 8, 77, 8, 205, 8, 45, 8,
  			173, 8, 109, 8, 237, 8, 29, 8, 157, 8, 93, 8, 221, 8, 61, 8, 189, 8, 125, 8, 253, 8, 19, 9, 275, 9, 147, 9, 403, 9, 83, 9, 339, 9, 211, 9, 467, 9,
  			51, 9, 307, 9, 179, 9, 435, 9, 115, 9, 371, 9, 243, 9, 499, 9, 11, 9, 267, 9, 139, 9, 395, 9, 75, 9, 331, 9, 203, 9, 459, 9, 43, 9, 299, 9, 171, 9,
  			427, 9, 107, 9, 363, 9, 235, 9, 491, 9, 27, 9, 283, 9, 155, 9, 411, 9, 91, 9, 347, 9, 219, 9, 475, 9, 59, 9, 315, 9, 187, 9, 443, 9, 123, 9, 379,
  			9, 251, 9, 507, 9, 7, 9, 263, 9, 135, 9, 391, 9, 71, 9, 327, 9, 199, 9, 455, 9, 39, 9, 295, 9, 167, 9, 423, 9, 103, 9, 359, 9, 231, 9, 487, 9, 23,
  			9, 279, 9, 151, 9, 407, 9, 87, 9, 343, 9, 215, 9, 471, 9, 55, 9, 311, 9, 183, 9, 439, 9, 119, 9, 375, 9, 247, 9, 503, 9, 15, 9, 271, 9, 143, 9,
  			399, 9, 79, 9, 335, 9, 207, 9, 463, 9, 47, 9, 303, 9, 175, 9, 431, 9, 111, 9, 367, 9, 239, 9, 495, 9, 31, 9, 287, 9, 159, 9, 415, 9, 95, 9, 351, 9,
  			223, 9, 479, 9, 63, 9, 319, 9, 191, 9, 447, 9, 127, 9, 383, 9, 255, 9, 511, 9, 0, 7, 64, 7, 32, 7, 96, 7, 16, 7, 80, 7, 48, 7, 112, 7, 8, 7, 72, 7,
  			40, 7, 104, 7, 24, 7, 88, 7, 56, 7, 120, 7, 4, 7, 68, 7, 36, 7, 100, 7, 20, 7, 84, 7, 52, 7, 116, 7, 3, 8, 131, 8, 67, 8, 195, 8, 35, 8, 163, 8,
  			99, 8, 227, 8 ];

  	StaticTree.static_dtree = [ 0, 5, 16, 5, 8, 5, 24, 5, 4, 5, 20, 5, 12, 5, 28, 5, 2, 5, 18, 5, 10, 5, 26, 5, 6, 5, 22, 5, 14, 5, 30, 5, 1, 5, 17, 5, 9, 5,
  			25, 5, 5, 5, 21, 5, 13, 5, 29, 5, 3, 5, 19, 5, 11, 5, 27, 5, 7, 5, 23, 5 ];

  	StaticTree.static_l_desc = new StaticTree(StaticTree.static_ltree, Tree.extra_lbits, LITERALS + 1, L_CODES, MAX_BITS);

  	StaticTree.static_d_desc = new StaticTree(StaticTree.static_dtree, Tree.extra_dbits, 0, D_CODES, MAX_BITS);

  	StaticTree.static_bl_desc = new StaticTree(null, Tree.extra_blbits, 0, BL_CODES, MAX_BL_BITS);

  	// Deflate

  	var MAX_MEM_LEVEL = 9;
  	var DEF_MEM_LEVEL = 8;

  	function Config(good_length, max_lazy, nice_length, max_chain, func) {
  		var that = this;
  		that.good_length = good_length;
  		that.max_lazy = max_lazy;
  		that.nice_length = nice_length;
  		that.max_chain = max_chain;
  		that.func = func;
  	}

  	var STORED = 0;
  	var FAST = 1;
  	var SLOW = 2;
  	var config_table = [ new Config(0, 0, 0, 0, STORED), new Config(4, 4, 8, 4, FAST), new Config(4, 5, 16, 8, FAST), new Config(4, 6, 32, 32, FAST),
  			new Config(4, 4, 16, 16, SLOW), new Config(8, 16, 32, 32, SLOW), new Config(8, 16, 128, 128, SLOW), new Config(8, 32, 128, 256, SLOW),
  			new Config(32, 128, 258, 1024, SLOW), new Config(32, 258, 258, 4096, SLOW) ];

  	var z_errmsg = [ "need dictionary", // Z_NEED_DICT
  	// 2
  	"stream end", // Z_STREAM_END 1
  	"", // Z_OK 0
  	"", // Z_ERRNO (-1)
  	"stream error", // Z_STREAM_ERROR (-2)
  	"data error", // Z_DATA_ERROR (-3)
  	"", // Z_MEM_ERROR (-4)
  	"buffer error", // Z_BUF_ERROR (-5)
  	"",// Z_VERSION_ERROR (-6)
  	"" ];

  	// block not completed, need more input or more output
  	var NeedMore = 0;

  	// block flush performed
  	var BlockDone = 1;

  	// finish started, need only more output at next deflate
  	var FinishStarted = 2;

  	// finish done, accept no more input or output
  	var FinishDone = 3;

  	// preset dictionary flag in zlib header
  	var PRESET_DICT = 0x20;

  	var INIT_STATE = 42;
  	var BUSY_STATE = 113;
  	var FINISH_STATE = 666;

  	// The deflate compression method
  	var Z_DEFLATED = 8;

  	var STORED_BLOCK = 0;
  	var STATIC_TREES = 1;
  	var DYN_TREES = 2;

  	var MIN_MATCH = 3;
  	var MAX_MATCH = 258;
  	var MIN_LOOKAHEAD = (MAX_MATCH + MIN_MATCH + 1);

  	function smaller(tree, n, m, depth) {
  		var tn2 = tree[n * 2];
  		var tm2 = tree[m * 2];
  		return (tn2 < tm2 || (tn2 == tm2 && depth[n] <= depth[m]));
  	}

  	function Deflate() {

  		var that = this;
  		var strm; // pointer back to this zlib stream
  		var status; // as the name implies
  		// pending_buf; // output still pending
  		var pending_buf_size; // size of pending_buf
  		// pending_out; // next pending byte to output to the stream
  		// pending; // nb of bytes in the pending buffer
  		var method; // STORED (for zip only) or DEFLATED
  		var last_flush; // value of flush param for previous deflate call

  		var w_size; // LZ77 window size (32K by default)
  		var w_bits; // log2(w_size) (8..16)
  		var w_mask; // w_size - 1

  		var window;
  		// Sliding window. Input bytes are read into the second half of the window,
  		// and move to the first half later to keep a dictionary of at least wSize
  		// bytes. With this organization, matches are limited to a distance of
  		// wSize-MAX_MATCH bytes, but this ensures that IO is always
  		// performed with a length multiple of the block size. Also, it limits
  		// the window size to 64K, which is quite useful on MSDOS.
  		// To do: use the user input buffer as sliding window.

  		var window_size;
  		// Actual size of window: 2*wSize, except when the user input buffer
  		// is directly used as sliding window.

  		var prev;
  		// Link to older string with same hash index. To limit the size of this
  		// array to 64K, this link is maintained only for the last 32K strings.
  		// An index in this array is thus a window index modulo 32K.

  		var head; // Heads of the hash chains or NIL.

  		var ins_h; // hash index of string to be inserted
  		var hash_size; // number of elements in hash table
  		var hash_bits; // log2(hash_size)
  		var hash_mask; // hash_size-1

  		// Number of bits by which ins_h must be shifted at each input
  		// step. It must be such that after MIN_MATCH steps, the oldest
  		// byte no longer takes part in the hash key, that is:
  		// hash_shift * MIN_MATCH >= hash_bits
  		var hash_shift;

  		// Window position at the beginning of the current output block. Gets
  		// negative when the window is moved backwards.

  		var block_start;

  		var match_length; // length of best match
  		var prev_match; // previous match
  		var match_available; // set if previous match exists
  		var strstart; // start of string to insert
  		var match_start; // start of matching string
  		var lookahead; // number of valid bytes ahead in window

  		// Length of the best match at previous step. Matches not greater than this
  		// are discarded. This is used in the lazy match evaluation.
  		var prev_length;

  		// To speed up deflation, hash chains are never searched beyond this
  		// length. A higher limit improves compression ratio but degrades the speed.
  		var max_chain_length;

  		// Attempt to find a better match only when the current match is strictly
  		// smaller than this value. This mechanism is used only for compression
  		// levels >= 4.
  		var max_lazy_match;

  		// Insert new strings in the hash table only if the match length is not
  		// greater than this length. This saves time but degrades compression.
  		// max_insert_length is used only for compression levels <= 3.

  		var level; // compression level (1..9)
  		var strategy; // favor or force Huffman coding

  		// Use a faster search when the previous match is longer than this
  		var good_match;

  		// Stop searching when current match exceeds this
  		var nice_match;

  		var dyn_ltree; // literal and length tree
  		var dyn_dtree; // distance tree
  		var bl_tree; // Huffman tree for bit lengths

  		var l_desc = new Tree(); // desc for literal tree
  		var d_desc = new Tree(); // desc for distance tree
  		var bl_desc = new Tree(); // desc for bit length tree

  		// that.heap_len; // number of elements in the heap
  		// that.heap_max; // element of largest frequency
  		// The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
  		// The same heap array is used to build all trees.

  		// Depth of each subtree used as tie breaker for trees of equal frequency
  		that.depth = [];

  		var l_buf; // index for literals or lengths */

  		// Size of match buffer for literals/lengths. There are 4 reasons for
  		// limiting lit_bufsize to 64K:
  		// - frequencies can be kept in 16 bit counters
  		// - if compression is not successful for the first block, all input
  		// data is still in the window so we can still emit a stored block even
  		// when input comes from standard input. (This can also be done for
  		// all blocks if lit_bufsize is not greater than 32K.)
  		// - if compression is not successful for a file smaller than 64K, we can
  		// even emit a stored file instead of a stored block (saving 5 bytes).
  		// This is applicable only for zip (not gzip or zlib).
  		// - creating new Huffman trees less frequently may not provide fast
  		// adaptation to changes in the input data statistics. (Take for
  		// example a binary file with poorly compressible code followed by
  		// a highly compressible string table.) Smaller buffer sizes give
  		// fast adaptation but have of course the overhead of transmitting
  		// trees more frequently.
  		// - I can't count above 4
  		var lit_bufsize;

  		var last_lit; // running index in l_buf

  		// Buffer for distances. To simplify the code, d_buf and l_buf have
  		// the same number of elements. To use different lengths, an extra flag
  		// array would be necessary.

  		var d_buf; // index of pendig_buf

  		// that.opt_len; // bit length of current block with optimal trees
  		// that.static_len; // bit length of current block with static trees
  		var matches; // number of string matches in current block
  		var last_eob_len; // bit length of EOB code for last block

  		// Output buffer. bits are inserted starting at the bottom (least
  		// significant bits).
  		var bi_buf;

  		// Number of valid bits in bi_buf. All bits above the last valid bit
  		// are always zero.
  		var bi_valid;

  		// number of codes at each bit length for an optimal tree
  		that.bl_count = [];

  		// heap used to build the Huffman trees
  		that.heap = [];

  		dyn_ltree = [];
  		dyn_dtree = [];
  		bl_tree = [];

  		function lm_init() {
  			var i;
  			window_size = 2 * w_size;

  			head[hash_size - 1] = 0;
  			for (i = 0; i < hash_size - 1; i++) {
  				head[i] = 0;
  			}

  			// Set the default configuration parameters:
  			max_lazy_match = config_table[level].max_lazy;
  			good_match = config_table[level].good_length;
  			nice_match = config_table[level].nice_length;
  			max_chain_length = config_table[level].max_chain;

  			strstart = 0;
  			block_start = 0;
  			lookahead = 0;
  			match_length = prev_length = MIN_MATCH - 1;
  			match_available = 0;
  			ins_h = 0;
  		}

  		function init_block() {
  			var i;
  			// Initialize the trees.
  			for (i = 0; i < L_CODES; i++)
  				dyn_ltree[i * 2] = 0;
  			for (i = 0; i < D_CODES; i++)
  				dyn_dtree[i * 2] = 0;
  			for (i = 0; i < BL_CODES; i++)
  				bl_tree[i * 2] = 0;

  			dyn_ltree[END_BLOCK * 2] = 1;
  			that.opt_len = that.static_len = 0;
  			last_lit = matches = 0;
  		}

  		// Initialize the tree data structures for a new zlib stream.
  		function tr_init() {

  			l_desc.dyn_tree = dyn_ltree;
  			l_desc.stat_desc = StaticTree.static_l_desc;

  			d_desc.dyn_tree = dyn_dtree;
  			d_desc.stat_desc = StaticTree.static_d_desc;

  			bl_desc.dyn_tree = bl_tree;
  			bl_desc.stat_desc = StaticTree.static_bl_desc;

  			bi_buf = 0;
  			bi_valid = 0;
  			last_eob_len = 8; // enough lookahead for inflate

  			// Initialize the first block of the first file:
  			init_block();
  		}

  		// Restore the heap property by moving down the tree starting at node k,
  		// exchanging a node with the smallest of its two sons if necessary,
  		// stopping
  		// when the heap property is re-established (each father smaller than its
  		// two sons).
  		that.pqdownheap = function(tree, // the tree to restore
  		k // node to move down
  		) {
  			var heap = that.heap;
  			var v = heap[k];
  			var j = k << 1; // left son of k
  			while (j <= that.heap_len) {
  				// Set j to the smallest of the two sons:
  				if (j < that.heap_len && smaller(tree, heap[j + 1], heap[j], that.depth)) {
  					j++;
  				}
  				// Exit if v is smaller than both sons
  				if (smaller(tree, v, heap[j], that.depth))
  					break;

  				// Exchange v with the smallest son
  				heap[k] = heap[j];
  				k = j;
  				// And continue down the tree, setting j to the left son of k
  				j <<= 1;
  			}
  			heap[k] = v;
  		};

  		// Scan a literal or distance tree to determine the frequencies of the codes
  		// in the bit length tree.
  		function scan_tree(tree,// the tree to be scanned
  		max_code // and its largest code of non zero frequency
  		) {
  			var n; // iterates over all tree elements
  			var prevlen = -1; // last emitted length
  			var curlen; // length of current code
  			var nextlen = tree[0 * 2 + 1]; // length of next code
  			var count = 0; // repeat count of the current code
  			var max_count = 7; // max repeat count
  			var min_count = 4; // min repeat count

  			if (nextlen === 0) {
  				max_count = 138;
  				min_count = 3;
  			}
  			tree[(max_code + 1) * 2 + 1] = 0xffff; // guard

  			for (n = 0; n <= max_code; n++) {
  				curlen = nextlen;
  				nextlen = tree[(n + 1) * 2 + 1];
  				if (++count < max_count && curlen == nextlen) {
  					continue;
  				} else if (count < min_count) {
  					bl_tree[curlen * 2] += count;
  				} else if (curlen !== 0) {
  					if (curlen != prevlen)
  						bl_tree[curlen * 2]++;
  					bl_tree[REP_3_6 * 2]++;
  				} else if (count <= 10) {
  					bl_tree[REPZ_3_10 * 2]++;
  				} else {
  					bl_tree[REPZ_11_138 * 2]++;
  				}
  				count = 0;
  				prevlen = curlen;
  				if (nextlen === 0) {
  					max_count = 138;
  					min_count = 3;
  				} else if (curlen == nextlen) {
  					max_count = 6;
  					min_count = 3;
  				} else {
  					max_count = 7;
  					min_count = 4;
  				}
  			}
  		}

  		// Construct the Huffman tree for the bit lengths and return the index in
  		// bl_order of the last bit length code to send.
  		function build_bl_tree() {
  			var max_blindex; // index of last bit length code of non zero freq

  			// Determine the bit length frequencies for literal and distance trees
  			scan_tree(dyn_ltree, l_desc.max_code);
  			scan_tree(dyn_dtree, d_desc.max_code);

  			// Build the bit length tree:
  			bl_desc.build_tree(that);
  			// opt_len now includes the length of the tree representations, except
  			// the lengths of the bit lengths codes and the 5+5+4 bits for the
  			// counts.

  			// Determine the number of bit length codes to send. The pkzip format
  			// requires that at least 4 bit length codes be sent. (appnote.txt says
  			// 3 but the actual value used is 4.)
  			for (max_blindex = BL_CODES - 1; max_blindex >= 3; max_blindex--) {
  				if (bl_tree[Tree.bl_order[max_blindex] * 2 + 1] !== 0)
  					break;
  			}
  			// Update opt_len to include the bit length tree and counts
  			that.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;

  			return max_blindex;
  		}

  		// Output a byte on the stream.
  		// IN assertion: there is enough room in pending_buf.
  		function put_byte(p) {
  			that.pending_buf[that.pending++] = p;
  		}

  		function put_short(w) {
  			put_byte(w & 0xff);
  			put_byte((w >>> 8) & 0xff);
  		}

  		function putShortMSB(b) {
  			put_byte((b >> 8) & 0xff);
  			put_byte((b & 0xff) & 0xff);
  		}

  		function send_bits(value, length) {
  			var val, len = length;
  			if (bi_valid > Buf_size - len) {
  				val = value;
  				// bi_buf |= (val << bi_valid);
  				bi_buf |= ((val << bi_valid) & 0xffff);
  				put_short(bi_buf);
  				bi_buf = val >>> (Buf_size - bi_valid);
  				bi_valid += len - Buf_size;
  			} else {
  				// bi_buf |= (value) << bi_valid;
  				bi_buf |= (((value) << bi_valid) & 0xffff);
  				bi_valid += len;
  			}
  		}

  		function send_code(c, tree) {
  			var c2 = c * 2;
  			send_bits(tree[c2] & 0xffff, tree[c2 + 1] & 0xffff);
  		}

  		// Send a literal or distance tree in compressed form, using the codes in
  		// bl_tree.
  		function send_tree(tree,// the tree to be sent
  		max_code // and its largest code of non zero frequency
  		) {
  			var n; // iterates over all tree elements
  			var prevlen = -1; // last emitted length
  			var curlen; // length of current code
  			var nextlen = tree[0 * 2 + 1]; // length of next code
  			var count = 0; // repeat count of the current code
  			var max_count = 7; // max repeat count
  			var min_count = 4; // min repeat count

  			if (nextlen === 0) {
  				max_count = 138;
  				min_count = 3;
  			}

  			for (n = 0; n <= max_code; n++) {
  				curlen = nextlen;
  				nextlen = tree[(n + 1) * 2 + 1];
  				if (++count < max_count && curlen == nextlen) {
  					continue;
  				} else if (count < min_count) {
  					do {
  						send_code(curlen, bl_tree);
  					} while (--count !== 0);
  				} else if (curlen !== 0) {
  					if (curlen != prevlen) {
  						send_code(curlen, bl_tree);
  						count--;
  					}
  					send_code(REP_3_6, bl_tree);
  					send_bits(count - 3, 2);
  				} else if (count <= 10) {
  					send_code(REPZ_3_10, bl_tree);
  					send_bits(count - 3, 3);
  				} else {
  					send_code(REPZ_11_138, bl_tree);
  					send_bits(count - 11, 7);
  				}
  				count = 0;
  				prevlen = curlen;
  				if (nextlen === 0) {
  					max_count = 138;
  					min_count = 3;
  				} else if (curlen == nextlen) {
  					max_count = 6;
  					min_count = 3;
  				} else {
  					max_count = 7;
  					min_count = 4;
  				}
  			}
  		}

  		// Send the header for a block using dynamic Huffman trees: the counts, the
  		// lengths of the bit length codes, the literal tree and the distance tree.
  		// IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
  		function send_all_trees(lcodes, dcodes, blcodes) {
  			var rank; // index in bl_order

  			send_bits(lcodes - 257, 5); // not +255 as stated in appnote.txt
  			send_bits(dcodes - 1, 5);
  			send_bits(blcodes - 4, 4); // not -3 as stated in appnote.txt
  			for (rank = 0; rank < blcodes; rank++) {
  				send_bits(bl_tree[Tree.bl_order[rank] * 2 + 1], 3);
  			}
  			send_tree(dyn_ltree, lcodes - 1); // literal tree
  			send_tree(dyn_dtree, dcodes - 1); // distance tree
  		}

  		// Flush the bit buffer, keeping at most 7 bits in it.
  		function bi_flush() {
  			if (bi_valid == 16) {
  				put_short(bi_buf);
  				bi_buf = 0;
  				bi_valid = 0;
  			} else if (bi_valid >= 8) {
  				put_byte(bi_buf & 0xff);
  				bi_buf >>>= 8;
  				bi_valid -= 8;
  			}
  		}

  		// Send one empty static block to give enough lookahead for inflate.
  		// This takes 10 bits, of which 7 may remain in the bit buffer.
  		// The current inflate code requires 9 bits of lookahead. If the
  		// last two codes for the previous block (real code plus EOB) were coded
  		// on 5 bits or less, inflate may have only 5+3 bits of lookahead to decode
  		// the last real code. In this case we send two empty static blocks instead
  		// of one. (There are no problems if the previous block is stored or fixed.)
  		// To simplify the code, we assume the worst case of last real code encoded
  		// on one bit only.
  		function _tr_align() {
  			send_bits(STATIC_TREES << 1, 3);
  			send_code(END_BLOCK, StaticTree.static_ltree);

  			bi_flush();

  			// Of the 10 bits for the empty block, we have already sent
  			// (10 - bi_valid) bits. The lookahead for the last real code (before
  			// the EOB of the previous block) was thus at least one plus the length
  			// of the EOB plus what we have just sent of the empty static block.
  			if (1 + last_eob_len + 10 - bi_valid < 9) {
  				send_bits(STATIC_TREES << 1, 3);
  				send_code(END_BLOCK, StaticTree.static_ltree);
  				bi_flush();
  			}
  			last_eob_len = 7;
  		}

  		// Save the match info and tally the frequency counts. Return true if
  		// the current block must be flushed.
  		function _tr_tally(dist, // distance of matched string
  		lc // match length-MIN_MATCH or unmatched char (if dist==0)
  		) {
  			var out_length, in_length, dcode;
  			that.pending_buf[d_buf + last_lit * 2] = (dist >>> 8) & 0xff;
  			that.pending_buf[d_buf + last_lit * 2 + 1] = dist & 0xff;

  			that.pending_buf[l_buf + last_lit] = lc & 0xff;
  			last_lit++;

  			if (dist === 0) {
  				// lc is the unmatched char
  				dyn_ltree[lc * 2]++;
  			} else {
  				matches++;
  				// Here, lc is the match length - MIN_MATCH
  				dist--; // dist = match distance - 1
  				dyn_ltree[(Tree._length_code[lc] + LITERALS + 1) * 2]++;
  				dyn_dtree[Tree.d_code(dist) * 2]++;
  			}

  			if ((last_lit & 0x1fff) === 0 && level > 2) {
  				// Compute an upper bound for the compressed length
  				out_length = last_lit * 8;
  				in_length = strstart - block_start;
  				for (dcode = 0; dcode < D_CODES; dcode++) {
  					out_length += dyn_dtree[dcode * 2] * (5 + Tree.extra_dbits[dcode]);
  				}
  				out_length >>>= 3;
  				if ((matches < Math.floor(last_lit / 2)) && out_length < Math.floor(in_length / 2))
  					return true;
  			}

  			return (last_lit == lit_bufsize - 1);
  			// We avoid equality with lit_bufsize because of wraparound at 64K
  			// on 16 bit machines and because stored blocks are restricted to
  			// 64K-1 bytes.
  		}

  		// Send the block data compressed using the given Huffman trees
  		function compress_block(ltree, dtree) {
  			var dist; // distance of matched string
  			var lc; // match length or unmatched char (if dist === 0)
  			var lx = 0; // running index in l_buf
  			var code; // the code to send
  			var extra; // number of extra bits to send

  			if (last_lit !== 0) {
  				do {
  					dist = ((that.pending_buf[d_buf + lx * 2] << 8) & 0xff00) | (that.pending_buf[d_buf + lx * 2 + 1] & 0xff);
  					lc = (that.pending_buf[l_buf + lx]) & 0xff;
  					lx++;

  					if (dist === 0) {
  						send_code(lc, ltree); // send a literal byte
  					} else {
  						// Here, lc is the match length - MIN_MATCH
  						code = Tree._length_code[lc];

  						send_code(code + LITERALS + 1, ltree); // send the length
  						// code
  						extra = Tree.extra_lbits[code];
  						if (extra !== 0) {
  							lc -= Tree.base_length[code];
  							send_bits(lc, extra); // send the extra length bits
  						}
  						dist--; // dist is now the match distance - 1
  						code = Tree.d_code(dist);

  						send_code(code, dtree); // send the distance code
  						extra = Tree.extra_dbits[code];
  						if (extra !== 0) {
  							dist -= Tree.base_dist[code];
  							send_bits(dist, extra); // send the extra distance bits
  						}
  					} // literal or match pair ?

  					// Check that the overlay between pending_buf and d_buf+l_buf is
  					// ok:
  				} while (lx < last_lit);
  			}

  			send_code(END_BLOCK, ltree);
  			last_eob_len = ltree[END_BLOCK * 2 + 1];
  		}

  		// Flush the bit buffer and align the output on a byte boundary
  		function bi_windup() {
  			if (bi_valid > 8) {
  				put_short(bi_buf);
  			} else if (bi_valid > 0) {
  				put_byte(bi_buf & 0xff);
  			}
  			bi_buf = 0;
  			bi_valid = 0;
  		}

  		// Copy a stored block, storing first the length and its
  		// one's complement if requested.
  		function copy_block(buf, // the input data
  		len, // its length
  		header // true if block header must be written
  		) {
  			bi_windup(); // align on byte boundary
  			last_eob_len = 8; // enough lookahead for inflate

  			if (header) {
  				put_short(len);
  				put_short(~len);
  			}

  			that.pending_buf.set(window.subarray(buf, buf + len), that.pending);
  			that.pending += len;
  		}

  		// Send a stored block
  		function _tr_stored_block(buf, // input block
  		stored_len, // length of input block
  		eof // true if this is the last block for a file
  		) {
  			send_bits((STORED_BLOCK << 1) + (eof ? 1 : 0), 3); // send block type
  			copy_block(buf, stored_len, true); // with header
  		}

  		// Determine the best encoding for the current block: dynamic trees, static
  		// trees or store, and output the encoded block to the zip file.
  		function _tr_flush_block(buf, // input block, or NULL if too old
  		stored_len, // length of input block
  		eof // true if this is the last block for a file
  		) {
  			var opt_lenb, static_lenb;// opt_len and static_len in bytes
  			var max_blindex = 0; // index of last bit length code of non zero freq

  			// Build the Huffman trees unless a stored block is forced
  			if (level > 0) {
  				// Construct the literal and distance trees
  				l_desc.build_tree(that);

  				d_desc.build_tree(that);

  				// At this point, opt_len and static_len are the total bit lengths
  				// of
  				// the compressed block data, excluding the tree representations.

  				// Build the bit length tree for the above two trees, and get the
  				// index
  				// in bl_order of the last bit length code to send.
  				max_blindex = build_bl_tree();

  				// Determine the best encoding. Compute first the block length in
  				// bytes
  				opt_lenb = (that.opt_len + 3 + 7) >>> 3;
  				static_lenb = (that.static_len + 3 + 7) >>> 3;

  				if (static_lenb <= opt_lenb)
  					opt_lenb = static_lenb;
  			} else {
  				opt_lenb = static_lenb = stored_len + 5; // force a stored block
  			}

  			if ((stored_len + 4 <= opt_lenb) && buf != -1) {
  				// 4: two words for the lengths
  				// The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
  				// Otherwise we can't have processed more than WSIZE input bytes
  				// since
  				// the last block flush, because compression would have been
  				// successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
  				// transform a block into a stored block.
  				_tr_stored_block(buf, stored_len, eof);
  			} else if (static_lenb == opt_lenb) {
  				send_bits((STATIC_TREES << 1) + (eof ? 1 : 0), 3);
  				compress_block(StaticTree.static_ltree, StaticTree.static_dtree);
  			} else {
  				send_bits((DYN_TREES << 1) + (eof ? 1 : 0), 3);
  				send_all_trees(l_desc.max_code + 1, d_desc.max_code + 1, max_blindex + 1);
  				compress_block(dyn_ltree, dyn_dtree);
  			}

  			// The above check is made mod 2^32, for files larger than 512 MB
  			// and uLong implemented on 32 bits.

  			init_block();

  			if (eof) {
  				bi_windup();
  			}
  		}

  		function flush_block_only(eof) {
  			_tr_flush_block(block_start >= 0 ? block_start : -1, strstart - block_start, eof);
  			block_start = strstart;
  			strm.flush_pending();
  		}

  		// Fill the window when the lookahead becomes insufficient.
  		// Updates strstart and lookahead.
  		//
  		// IN assertion: lookahead < MIN_LOOKAHEAD
  		// OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
  		// At least one byte has been read, or avail_in === 0; reads are
  		// performed for at least two bytes (required for the zip translate_eol
  		// option -- not supported here).
  		function fill_window() {
  			var n, m;
  			var p;
  			var more; // Amount of free space at the end of the window.

  			do {
  				more = (window_size - lookahead - strstart);

  				// Deal with !@#$% 64K limit:
  				if (more === 0 && strstart === 0 && lookahead === 0) {
  					more = w_size;
  				} else if (more == -1) {
  					// Very unlikely, but possible on 16 bit machine if strstart ==
  					// 0
  					// and lookahead == 1 (input done one byte at time)
  					more--;

  					// If the window is almost full and there is insufficient
  					// lookahead,
  					// move the upper half to the lower one to make room in the
  					// upper half.
  				} else if (strstart >= w_size + w_size - MIN_LOOKAHEAD) {
  					window.set(window.subarray(w_size, w_size + w_size), 0);

  					match_start -= w_size;
  					strstart -= w_size; // we now have strstart >= MAX_DIST
  					block_start -= w_size;

  					// Slide the hash table (could be avoided with 32 bit values
  					// at the expense of memory usage). We slide even when level ==
  					// 0
  					// to keep the hash table consistent if we switch back to level
  					// > 0
  					// later. (Using level 0 permanently is not an optimal usage of
  					// zlib, so we don't care about this pathological case.)

  					n = hash_size;
  					p = n;
  					do {
  						m = (head[--p] & 0xffff);
  						head[p] = (m >= w_size ? m - w_size : 0);
  					} while (--n !== 0);

  					n = w_size;
  					p = n;
  					do {
  						m = (prev[--p] & 0xffff);
  						prev[p] = (m >= w_size ? m - w_size : 0);
  						// If n is not on any hash chain, prev[n] is garbage but
  						// its value will never be used.
  					} while (--n !== 0);
  					more += w_size;
  				}

  				if (strm.avail_in === 0)
  					return;

  				// If there was no sliding:
  				// strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
  				// more == window_size - lookahead - strstart
  				// => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
  				// => more >= window_size - 2*WSIZE + 2
  				// In the BIG_MEM or MMAP case (not yet supported),
  				// window_size == input_size + MIN_LOOKAHEAD &&
  				// strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
  				// Otherwise, window_size == 2*WSIZE so more >= 2.
  				// If there was sliding, more >= WSIZE. So in all cases, more >= 2.

  				n = strm.read_buf(window, strstart + lookahead, more);
  				lookahead += n;

  				// Initialize the hash value now that we have some input:
  				if (lookahead >= MIN_MATCH) {
  					ins_h = window[strstart] & 0xff;
  					ins_h = (((ins_h) << hash_shift) ^ (window[strstart + 1] & 0xff)) & hash_mask;
  				}
  				// If the whole input has less than MIN_MATCH bytes, ins_h is
  				// garbage,
  				// but this is not important since only literal bytes will be
  				// emitted.
  			} while (lookahead < MIN_LOOKAHEAD && strm.avail_in !== 0);
  		}

  		// Copy without compression as much as possible from the input stream,
  		// return
  		// the current block state.
  		// This function does not insert new strings in the dictionary since
  		// uncompressible data is probably not useful. This function is used
  		// only for the level=0 compression option.
  		// NOTE: this function should be optimized to avoid extra copying from
  		// window to pending_buf.
  		function deflate_stored(flush) {
  			// Stored blocks are limited to 0xffff bytes, pending_buf is limited
  			// to pending_buf_size, and each stored block has a 5 byte header:

  			var max_block_size = 0xffff;
  			var max_start;

  			if (max_block_size > pending_buf_size - 5) {
  				max_block_size = pending_buf_size - 5;
  			}

  			// Copy as much as possible from input to output:
  			while (true) {
  				// Fill the window as much as possible:
  				if (lookahead <= 1) {
  					fill_window();
  					if (lookahead === 0 && flush == Z_NO_FLUSH)
  						return NeedMore;
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				strstart += lookahead;
  				lookahead = 0;

  				// Emit a stored block if pending_buf will be full:
  				max_start = block_start + max_block_size;
  				if (strstart === 0 || strstart >= max_start) {
  					// strstart === 0 is possible when wraparound on 16-bit machine
  					lookahead = (strstart - max_start);
  					strstart = max_start;

  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;

  				}

  				// Flush if we may have to slide, otherwise block_start may become
  				// negative and the data will be gone:
  				if (strstart - block_start >= w_size - MIN_LOOKAHEAD) {
  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;
  				}
  			}

  			flush_block_only(flush == Z_FINISH);
  			if (strm.avail_out === 0)
  				return (flush == Z_FINISH) ? FinishStarted : NeedMore;

  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		function longest_match(cur_match) {
  			var chain_length = max_chain_length; // max hash chain length
  			var scan = strstart; // current string
  			var match; // matched string
  			var len; // length of current match
  			var best_len = prev_length; // best match length so far
  			var limit = strstart > (w_size - MIN_LOOKAHEAD) ? strstart - (w_size - MIN_LOOKAHEAD) : 0;
  			var _nice_match = nice_match;

  			// Stop when cur_match becomes <= limit. To simplify the code,
  			// we prevent matches with the string of window index 0.

  			var wmask = w_mask;

  			var strend = strstart + MAX_MATCH;
  			var scan_end1 = window[scan + best_len - 1];
  			var scan_end = window[scan + best_len];

  			// The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of
  			// 16.
  			// It is easy to get rid of this optimization if necessary.

  			// Do not waste too much time if we already have a good match:
  			if (prev_length >= good_match) {
  				chain_length >>= 2;
  			}

  			// Do not look for matches beyond the end of the input. This is
  			// necessary
  			// to make deflate deterministic.
  			if (_nice_match > lookahead)
  				_nice_match = lookahead;

  			do {
  				match = cur_match;

  				// Skip to next match if the match length cannot increase
  				// or if the match length is less than 2:
  				if (window[match + best_len] != scan_end || window[match + best_len - 1] != scan_end1 || window[match] != window[scan]
  						|| window[++match] != window[scan + 1])
  					continue;

  				// The check at best_len-1 can be removed because it will be made
  				// again later. (This heuristic is not always a win.)
  				// It is not necessary to compare scan[2] and match[2] since they
  				// are always equal when the other bytes match, given that
  				// the hash keys are equal and that HASH_BITS >= 8.
  				scan += 2;
  				match++;

  				// We check for insufficient lookahead only every 8th comparison;
  				// the 256th check will be made at strstart+258.
  				do {
  				} while (window[++scan] == window[++match] && window[++scan] == window[++match] && window[++scan] == window[++match]
  						&& window[++scan] == window[++match] && window[++scan] == window[++match] && window[++scan] == window[++match]
  						&& window[++scan] == window[++match] && window[++scan] == window[++match] && scan < strend);

  				len = MAX_MATCH - (strend - scan);
  				scan = strend - MAX_MATCH;

  				if (len > best_len) {
  					match_start = cur_match;
  					best_len = len;
  					if (len >= _nice_match)
  						break;
  					scan_end1 = window[scan + best_len - 1];
  					scan_end = window[scan + best_len];
  				}

  			} while ((cur_match = (prev[cur_match & wmask] & 0xffff)) > limit && --chain_length !== 0);

  			if (best_len <= lookahead)
  				return best_len;
  			return lookahead;
  		}

  		// Compress as much as possible from the input stream, return the current
  		// block state.
  		// This function does not perform lazy evaluation of matches and inserts
  		// new strings in the dictionary only for unmatched strings or for short
  		// matches. It is used only for the fast compression options.
  		function deflate_fast(flush) {
  			// short hash_head = 0; // head of the hash chain
  			var hash_head = 0; // head of the hash chain
  			var bflush; // set if current block must be flushed

  			while (true) {
  				// Make sure that we always have enough lookahead, except
  				// at the end of the input file. We need MAX_MATCH bytes
  				// for the next match, plus MIN_MATCH bytes to insert the
  				// string following the next match.
  				if (lookahead < MIN_LOOKAHEAD) {
  					fill_window();
  					if (lookahead < MIN_LOOKAHEAD && flush == Z_NO_FLUSH) {
  						return NeedMore;
  					}
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				// Insert the string window[strstart .. strstart+2] in the
  				// dictionary, and set hash_head to the head of the hash chain:
  				if (lookahead >= MIN_MATCH) {
  					ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;

  					// prev[strstart&w_mask]=hash_head=head[ins_h];
  					hash_head = (head[ins_h] & 0xffff);
  					prev[strstart & w_mask] = head[ins_h];
  					head[ins_h] = strstart;
  				}

  				// Find the longest match, discarding those <= prev_length.
  				// At this point we have always match_length < MIN_MATCH

  				if (hash_head !== 0 && ((strstart - hash_head) & 0xffff) <= w_size - MIN_LOOKAHEAD) {
  					// To simplify the code, we prevent matches with the string
  					// of window index 0 (in particular we have to avoid a match
  					// of the string with itself at the start of the input file).
  					if (strategy != Z_HUFFMAN_ONLY) {
  						match_length = longest_match(hash_head);
  					}
  					// longest_match() sets match_start
  				}
  				if (match_length >= MIN_MATCH) {
  					// check_match(strstart, match_start, match_length);

  					bflush = _tr_tally(strstart - match_start, match_length - MIN_MATCH);

  					lookahead -= match_length;

  					// Insert new strings in the hash table only if the match length
  					// is not too large. This saves time but degrades compression.
  					if (match_length <= max_lazy_match && lookahead >= MIN_MATCH) {
  						match_length--; // string at strstart already in hash table
  						do {
  							strstart++;

  							ins_h = ((ins_h << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  							// prev[strstart&w_mask]=hash_head=head[ins_h];
  							hash_head = (head[ins_h] & 0xffff);
  							prev[strstart & w_mask] = head[ins_h];
  							head[ins_h] = strstart;

  							// strstart never exceeds WSIZE-MAX_MATCH, so there are
  							// always MIN_MATCH bytes ahead.
  						} while (--match_length !== 0);
  						strstart++;
  					} else {
  						strstart += match_length;
  						match_length = 0;
  						ins_h = window[strstart] & 0xff;

  						ins_h = (((ins_h) << hash_shift) ^ (window[strstart + 1] & 0xff)) & hash_mask;
  						// If lookahead < MIN_MATCH, ins_h is garbage, but it does
  						// not
  						// matter since it will be recomputed at next deflate call.
  					}
  				} else {
  					// No match, output a literal byte

  					bflush = _tr_tally(0, window[strstart] & 0xff);
  					lookahead--;
  					strstart++;
  				}
  				if (bflush) {

  					flush_block_only(false);
  					if (strm.avail_out === 0)
  						return NeedMore;
  				}
  			}

  			flush_block_only(flush == Z_FINISH);
  			if (strm.avail_out === 0) {
  				if (flush == Z_FINISH)
  					return FinishStarted;
  				else
  					return NeedMore;
  			}
  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		// Same as above, but achieves better compression. We use a lazy
  		// evaluation for matches: a match is finally adopted only if there is
  		// no better match at the next window position.
  		function deflate_slow(flush) {
  			// short hash_head = 0; // head of hash chain
  			var hash_head = 0; // head of hash chain
  			var bflush; // set if current block must be flushed
  			var max_insert;

  			// Process the input block.
  			while (true) {
  				// Make sure that we always have enough lookahead, except
  				// at the end of the input file. We need MAX_MATCH bytes
  				// for the next match, plus MIN_MATCH bytes to insert the
  				// string following the next match.

  				if (lookahead < MIN_LOOKAHEAD) {
  					fill_window();
  					if (lookahead < MIN_LOOKAHEAD && flush == Z_NO_FLUSH) {
  						return NeedMore;
  					}
  					if (lookahead === 0)
  						break; // flush the current block
  				}

  				// Insert the string window[strstart .. strstart+2] in the
  				// dictionary, and set hash_head to the head of the hash chain:

  				if (lookahead >= MIN_MATCH) {
  					ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  					// prev[strstart&w_mask]=hash_head=head[ins_h];
  					hash_head = (head[ins_h] & 0xffff);
  					prev[strstart & w_mask] = head[ins_h];
  					head[ins_h] = strstart;
  				}

  				// Find the longest match, discarding those <= prev_length.
  				prev_length = match_length;
  				prev_match = match_start;
  				match_length = MIN_MATCH - 1;

  				if (hash_head !== 0 && prev_length < max_lazy_match && ((strstart - hash_head) & 0xffff) <= w_size - MIN_LOOKAHEAD) {
  					// To simplify the code, we prevent matches with the string
  					// of window index 0 (in particular we have to avoid a match
  					// of the string with itself at the start of the input file).

  					if (strategy != Z_HUFFMAN_ONLY) {
  						match_length = longest_match(hash_head);
  					}
  					// longest_match() sets match_start

  					if (match_length <= 5 && (strategy == Z_FILTERED || (match_length == MIN_MATCH && strstart - match_start > 4096))) {

  						// If prev_match is also MIN_MATCH, match_start is garbage
  						// but we will ignore the current match anyway.
  						match_length = MIN_MATCH - 1;
  					}
  				}

  				// If there was a match at the previous step and the current
  				// match is not better, output the previous match:
  				if (prev_length >= MIN_MATCH && match_length <= prev_length) {
  					max_insert = strstart + lookahead - MIN_MATCH;
  					// Do not insert strings in hash table beyond this.

  					// check_match(strstart-1, prev_match, prev_length);

  					bflush = _tr_tally(strstart - 1 - prev_match, prev_length - MIN_MATCH);

  					// Insert in hash table all strings up to the end of the match.
  					// strstart-1 and strstart are already inserted. If there is not
  					// enough lookahead, the last two strings are not inserted in
  					// the hash table.
  					lookahead -= prev_length - 1;
  					prev_length -= 2;
  					do {
  						if (++strstart <= max_insert) {
  							ins_h = (((ins_h) << hash_shift) ^ (window[(strstart) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  							// prev[strstart&w_mask]=hash_head=head[ins_h];
  							hash_head = (head[ins_h] & 0xffff);
  							prev[strstart & w_mask] = head[ins_h];
  							head[ins_h] = strstart;
  						}
  					} while (--prev_length !== 0);
  					match_available = 0;
  					match_length = MIN_MATCH - 1;
  					strstart++;

  					if (bflush) {
  						flush_block_only(false);
  						if (strm.avail_out === 0)
  							return NeedMore;
  					}
  				} else if (match_available !== 0) {

  					// If there was no match at the previous position, output a
  					// single literal. If there was a match but the current match
  					// is longer, truncate the previous match to a single literal.

  					bflush = _tr_tally(0, window[strstart - 1] & 0xff);

  					if (bflush) {
  						flush_block_only(false);
  					}
  					strstart++;
  					lookahead--;
  					if (strm.avail_out === 0)
  						return NeedMore;
  				} else {
  					// There is no previous match to compare with, wait for
  					// the next step to decide.

  					match_available = 1;
  					strstart++;
  					lookahead--;
  				}
  			}

  			if (match_available !== 0) {
  				bflush = _tr_tally(0, window[strstart - 1] & 0xff);
  				match_available = 0;
  			}
  			flush_block_only(flush == Z_FINISH);

  			if (strm.avail_out === 0) {
  				if (flush == Z_FINISH)
  					return FinishStarted;
  				else
  					return NeedMore;
  			}

  			return flush == Z_FINISH ? FinishDone : BlockDone;
  		}

  		function deflateReset(strm) {
  			strm.total_in = strm.total_out = 0;
  			strm.msg = null; //
  			
  			that.pending = 0;
  			that.pending_out = 0;

  			status = BUSY_STATE;

  			last_flush = Z_NO_FLUSH;

  			tr_init();
  			lm_init();
  			return Z_OK;
  		}

  		that.deflateInit = function(strm, _level, bits, _method, memLevel, _strategy) {
  			if (!_method)
  				_method = Z_DEFLATED;
  			if (!memLevel)
  				memLevel = DEF_MEM_LEVEL;
  			if (!_strategy)
  				_strategy = Z_DEFAULT_STRATEGY;

  			// byte[] my_version=ZLIB_VERSION;

  			//
  			// if (!version || version[0] != my_version[0]
  			// || stream_size != sizeof(z_stream)) {
  			// return Z_VERSION_ERROR;
  			// }

  			strm.msg = null;

  			if (_level == Z_DEFAULT_COMPRESSION)
  				_level = 6;

  			if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || _method != Z_DEFLATED || bits < 9 || bits > 15 || _level < 0 || _level > 9 || _strategy < 0
  					|| _strategy > Z_HUFFMAN_ONLY) {
  				return Z_STREAM_ERROR;
  			}

  			strm.dstate = that;

  			w_bits = bits;
  			w_size = 1 << w_bits;
  			w_mask = w_size - 1;

  			hash_bits = memLevel + 7;
  			hash_size = 1 << hash_bits;
  			hash_mask = hash_size - 1;
  			hash_shift = Math.floor((hash_bits + MIN_MATCH - 1) / MIN_MATCH);

  			window = new Uint8Array(w_size * 2);
  			prev = [];
  			head = [];

  			lit_bufsize = 1 << (memLevel + 6); // 16K elements by default

  			// We overlay pending_buf and d_buf+l_buf. This works since the average
  			// output size for (length,distance) codes is <= 24 bits.
  			that.pending_buf = new Uint8Array(lit_bufsize * 4);
  			pending_buf_size = lit_bufsize * 4;

  			d_buf = Math.floor(lit_bufsize / 2);
  			l_buf = (1 + 2) * lit_bufsize;

  			level = _level;

  			strategy = _strategy;
  			method = _method & 0xff;

  			return deflateReset(strm);
  		};

  		that.deflateEnd = function() {
  			if (status != INIT_STATE && status != BUSY_STATE && status != FINISH_STATE) {
  				return Z_STREAM_ERROR;
  			}
  			// Deallocate in reverse order of allocations:
  			that.pending_buf = null;
  			head = null;
  			prev = null;
  			window = null;
  			// free
  			that.dstate = null;
  			return status == BUSY_STATE ? Z_DATA_ERROR : Z_OK;
  		};

  		that.deflateParams = function(strm, _level, _strategy) {
  			var err = Z_OK;

  			if (_level == Z_DEFAULT_COMPRESSION) {
  				_level = 6;
  			}
  			if (_level < 0 || _level > 9 || _strategy < 0 || _strategy > Z_HUFFMAN_ONLY) {
  				return Z_STREAM_ERROR;
  			}

  			if (config_table[level].func != config_table[_level].func && strm.total_in !== 0) {
  				// Flush the last buffer:
  				err = strm.deflate(Z_PARTIAL_FLUSH);
  			}

  			if (level != _level) {
  				level = _level;
  				max_lazy_match = config_table[level].max_lazy;
  				good_match = config_table[level].good_length;
  				nice_match = config_table[level].nice_length;
  				max_chain_length = config_table[level].max_chain;
  			}
  			strategy = _strategy;
  			return err;
  		};

  		that.deflateSetDictionary = function(strm, dictionary, dictLength) {
  			var length = dictLength;
  			var n, index = 0;

  			if (!dictionary || status != INIT_STATE)
  				return Z_STREAM_ERROR;

  			if (length < MIN_MATCH)
  				return Z_OK;
  			if (length > w_size - MIN_LOOKAHEAD) {
  				length = w_size - MIN_LOOKAHEAD;
  				index = dictLength - length; // use the tail of the dictionary
  			}
  			window.set(dictionary.subarray(index, index + length), 0);

  			strstart = length;
  			block_start = length;

  			// Insert all strings in the hash table (except for the last two bytes).
  			// s->lookahead stays null, so s->ins_h will be recomputed at the next
  			// call of fill_window.

  			ins_h = window[0] & 0xff;
  			ins_h = (((ins_h) << hash_shift) ^ (window[1] & 0xff)) & hash_mask;

  			for (n = 0; n <= length - MIN_MATCH; n++) {
  				ins_h = (((ins_h) << hash_shift) ^ (window[(n) + (MIN_MATCH - 1)] & 0xff)) & hash_mask;
  				prev[n & w_mask] = head[ins_h];
  				head[ins_h] = n;
  			}
  			return Z_OK;
  		};

  		that.deflate = function(_strm, flush) {
  			var i, header, level_flags, old_flush, bstate;

  			if (flush > Z_FINISH || flush < 0) {
  				return Z_STREAM_ERROR;
  			}

  			if (!_strm.next_out || (!_strm.next_in && _strm.avail_in !== 0) || (status == FINISH_STATE && flush != Z_FINISH)) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_STREAM_ERROR)];
  				return Z_STREAM_ERROR;
  			}
  			if (_strm.avail_out === 0) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			strm = _strm; // just in case
  			old_flush = last_flush;
  			last_flush = flush;

  			// Write the zlib header
  			if (status == INIT_STATE) {
  				header = (Z_DEFLATED + ((w_bits - 8) << 4)) << 8;
  				level_flags = ((level - 1) & 0xff) >> 1;

  				if (level_flags > 3)
  					level_flags = 3;
  				header |= (level_flags << 6);
  				if (strstart !== 0)
  					header |= PRESET_DICT;
  				header += 31 - (header % 31);

  				status = BUSY_STATE;
  				putShortMSB(header);
  			}

  			// Flush as much pending output as possible
  			if (that.pending !== 0) {
  				strm.flush_pending();
  				if (strm.avail_out === 0) {
  					// console.log(" avail_out==0");
  					// Since avail_out is 0, deflate will be called again with
  					// more output space, but possibly with both pending and
  					// avail_in equal to zero. There won't be anything to do,
  					// but this is not an error situation so make sure we
  					// return OK instead of BUF_ERROR at next call of deflate:
  					last_flush = -1;
  					return Z_OK;
  				}

  				// Make sure there is something to do and avoid duplicate
  				// consecutive
  				// flushes. For repeated and useless calls with Z_FINISH, we keep
  				// returning Z_STREAM_END instead of Z_BUFF_ERROR.
  			} else if (strm.avail_in === 0 && flush <= old_flush && flush != Z_FINISH) {
  				strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			// User must not provide more input after the first FINISH:
  			if (status == FINISH_STATE && strm.avail_in !== 0) {
  				_strm.msg = z_errmsg[Z_NEED_DICT - (Z_BUF_ERROR)];
  				return Z_BUF_ERROR;
  			}

  			// Start a new block or continue the current one.
  			if (strm.avail_in !== 0 || lookahead !== 0 || (flush != Z_NO_FLUSH && status != FINISH_STATE)) {
  				bstate = -1;
  				switch (config_table[level].func) {
  				case STORED:
  					bstate = deflate_stored(flush);
  					break;
  				case FAST:
  					bstate = deflate_fast(flush);
  					break;
  				case SLOW:
  					bstate = deflate_slow(flush);
  					break;
  				default:
  				}

  				if (bstate == FinishStarted || bstate == FinishDone) {
  					status = FINISH_STATE;
  				}
  				if (bstate == NeedMore || bstate == FinishStarted) {
  					if (strm.avail_out === 0) {
  						last_flush = -1; // avoid BUF_ERROR next call, see above
  					}
  					return Z_OK;
  					// If flush != Z_NO_FLUSH && avail_out === 0, the next call
  					// of deflate should use the same flush parameter to make sure
  					// that the flush is complete. So we don't have to output an
  					// empty block here, this will be done at next call. This also
  					// ensures that for a very small output buffer, we emit at most
  					// one empty block.
  				}

  				if (bstate == BlockDone) {
  					if (flush == Z_PARTIAL_FLUSH) {
  						_tr_align();
  					} else { // FULL_FLUSH or SYNC_FLUSH
  						_tr_stored_block(0, 0, false);
  						// For a full flush, this empty block will be recognized
  						// as a special marker by inflate_sync().
  						if (flush == Z_FULL_FLUSH) {
  							// state.head[s.hash_size-1]=0;
  							for (i = 0; i < hash_size/*-1*/; i++)
  								// forget history
  								head[i] = 0;
  						}
  					}
  					strm.flush_pending();
  					if (strm.avail_out === 0) {
  						last_flush = -1; // avoid BUF_ERROR at next call, see above
  						return Z_OK;
  					}
  				}
  			}

  			if (flush != Z_FINISH)
  				return Z_OK;
  			return Z_STREAM_END;
  		};
  	}

  	// ZStream

  	function ZStream() {
  		var that = this;
  		that.next_in_index = 0;
  		that.next_out_index = 0;
  		// that.next_in; // next input byte
  		that.avail_in = 0; // number of bytes available at next_in
  		that.total_in = 0; // total nb of input bytes read so far
  		// that.next_out; // next output byte should be put there
  		that.avail_out = 0; // remaining free space at next_out
  		that.total_out = 0; // total nb of bytes output so far
  		// that.msg;
  		// that.dstate;
  	}

  	ZStream.prototype = {
  		deflateInit : function(level, bits) {
  			var that = this;
  			that.dstate = new Deflate();
  			if (!bits)
  				bits = MAX_BITS;
  			return that.dstate.deflateInit(that, level, bits);
  		},

  		deflate : function(flush) {
  			var that = this;
  			if (!that.dstate) {
  				return Z_STREAM_ERROR;
  			}
  			return that.dstate.deflate(that, flush);
  		},

  		deflateEnd : function() {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			var ret = that.dstate.deflateEnd();
  			that.dstate = null;
  			return ret;
  		},

  		deflateParams : function(level, strategy) {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			return that.dstate.deflateParams(that, level, strategy);
  		},

  		deflateSetDictionary : function(dictionary, dictLength) {
  			var that = this;
  			if (!that.dstate)
  				return Z_STREAM_ERROR;
  			return that.dstate.deflateSetDictionary(that, dictionary, dictLength);
  		},

  		// Read a new buffer from the current input stream, update the
  		// total number of bytes read. All deflate() input goes through
  		// this function so some applications may wish to modify it to avoid
  		// allocating a large strm->next_in buffer and copying from it.
  		// (See also flush_pending()).
  		read_buf : function(buf, start, size) {
  			var that = this;
  			var len = that.avail_in;
  			if (len > size)
  				len = size;
  			if (len === 0)
  				return 0;
  			that.avail_in -= len;
  			buf.set(that.next_in.subarray(that.next_in_index, that.next_in_index + len), start);
  			that.next_in_index += len;
  			that.total_in += len;
  			return len;
  		},

  		// Flush as much pending output as possible. All deflate() output goes
  		// through this function so some applications may wish to modify it
  		// to avoid allocating a large strm->next_out buffer and copying into it.
  		// (See also read_buf()).
  		flush_pending : function() {
  			var that = this;
  			var len = that.dstate.pending;

  			if (len > that.avail_out)
  				len = that.avail_out;
  			if (len === 0)
  				return;

  			// if (that.dstate.pending_buf.length <= that.dstate.pending_out || that.next_out.length <= that.next_out_index
  			// || that.dstate.pending_buf.length < (that.dstate.pending_out + len) || that.next_out.length < (that.next_out_index +
  			// len)) {
  			// console.log(that.dstate.pending_buf.length + ", " + that.dstate.pending_out + ", " + that.next_out.length + ", " +
  			// that.next_out_index + ", " + len);
  			// console.log("avail_out=" + that.avail_out);
  			// }

  			that.next_out.set(that.dstate.pending_buf.subarray(that.dstate.pending_out, that.dstate.pending_out + len), that.next_out_index);

  			that.next_out_index += len;
  			that.dstate.pending_out += len;
  			that.total_out += len;
  			that.avail_out -= len;
  			that.dstate.pending -= len;
  			if (that.dstate.pending === 0) {
  				that.dstate.pending_out = 0;
  			}
  		}
  	};

  	// Deflater

  	return function Deflater(level) {
  		var that = this;
  		var z = new ZStream();
  		var bufsize = 512;
  		var flush = Z_NO_FLUSH;
  		var buf = new Uint8Array(bufsize);

  		if (typeof level == "undefined")
  			level = Z_DEFAULT_COMPRESSION;
  		z.deflateInit(level);
  		z.next_out = buf;

  		that.append = function(data, onprogress) {
  			var err, buffers = [], lastIndex = 0, bufferIndex = 0, bufferSize = 0, array;
  			if (!data.length)
  				return;
  			z.next_in_index = 0;
  			z.next_in = data;
  			z.avail_in = data.length;
  			do {
  				z.next_out_index = 0;
  				z.avail_out = bufsize;
  				err = z.deflate(flush);
  				if (err != Z_OK)
  					throw "deflating: " + z.msg;
  				if (z.next_out_index)
  					if (z.next_out_index == bufsize)
  						buffers.push(new Uint8Array(buf));
  					else
  						buffers.push(new Uint8Array(buf.subarray(0, z.next_out_index)));
  				bufferSize += z.next_out_index;
  				if (onprogress && z.next_in_index > 0 && z.next_in_index != lastIndex) {
  					onprogress(z.next_in_index);
  					lastIndex = z.next_in_index;
  				}
  			} while (z.avail_in > 0 || z.avail_out === 0);
  			array = new Uint8Array(bufferSize);
  			buffers.forEach(function(chunk) {
  				array.set(chunk, bufferIndex);
  				bufferIndex += chunk.length;
  			});
  			return array;
  		};
  		that.flush = function() {
  			var err, buffers = [], bufferIndex = 0, bufferSize = 0, array;
  			do {
  				z.next_out_index = 0;
  				z.avail_out = bufsize;
  				err = z.deflate(Z_FINISH);
  				if (err != Z_STREAM_END && err != Z_OK)
  					throw "deflating: " + z.msg;
  				if (bufsize - z.avail_out > 0)
  					buffers.push(new Uint8Array(buf.subarray(0, z.next_out_index)));
  				bufferSize += z.next_out_index;
  			} while (z.avail_in > 0 || z.avail_out === 0);
  			z.deflateEnd();
  			array = new Uint8Array(bufferSize);
  			buffers.forEach(function(chunk) {
  				array.set(chunk, bufferIndex);
  				bufferIndex += chunk.length;
  			});
  			return array;
  		};
  	};
  })(undefined);
  

  /*
    html2canvas 0.5.0-alpha <http://html2canvas.hertzen.com>
    Copyright (c) 2014 Niklas von Hertzen

    Released under MIT License
  */

  (function(window, document, module, exports, global, define, undefined){

  /*
   Copyright (c) 2013 Yehuda Katz, Tom Dale, and contributors

   
   */
  !function(){var a,b,c,d;!function(){var e={},f={};a=function(a,b,c){e[a]={deps:b,callback:c}},d=c=b=function(a){function c(b){if("."!==b.charAt(0))return b;for(var c=b.split("/"),d=a.split("/").slice(0,-1),e=0,f=c.length;f>e;e++){var g=c[e];if(".."===g)d.pop();else{if("."===g)continue;d.push(g)}}return d.join("/")}if(d._eak_seen=e,f[a])return f[a];if(f[a]={},!e[a])throw new Error("Could not find module "+a);for(var g,h=e[a],i=h.deps,j=h.callback,k=[],l=0,m=i.length;m>l;l++)"exports"===i[l]?k.push(g={}):k.push(b(c(i[l])));var n=j.apply(this,k);return f[a]=g||n}}(),a("promise/all",["./utils","exports"],function(a,b){"use strict";function c(a){var b=this;if(!d(a))throw new TypeError("You must pass an array to all.");return new b(function(b,c){function d(a){return function(b){f(a,b)}}function f(a,c){h[a]=c,0===--i&&b(h)}var g,h=[],i=a.length;0===i&&b([]);for(var j=0;j<a.length;j++)g=a[j],g&&e(g.then)?g.then(d(j),c):f(j,g)})}var d=a.isArray,e=a.isFunction;b.all=c}),a("promise/asap",["exports"],function(a){"use strict";function b(){return function(){process.nextTick(e)}}function c(){var a=0,b=new i(e),c=document.createTextNode("");return b.observe(c,{characterData:!0}),function(){c.data=a=++a%2}}function d(){return function(){j.setTimeout(e,1)}}function e(){for(var a=0;a<k.length;a++){var b=k[a],c=b[0],d=b[1];c(d)}k=[]}function f(a,b){var c=k.push([a,b]);1===c&&g()}var g,h="undefined"!=typeof window?window:{},i=h.MutationObserver||h.WebKitMutationObserver,j="undefined"!=typeof global?global:this,k=[];g="undefined"!=typeof process&&"[object process]"==={}.toString.call(process)?b():i?c():d(),a.asap=f}),a("promise/cast",["exports"],function(a){"use strict";function b(a){if(a&&"object"==typeof a&&a.constructor===this)return a;var b=this;return new b(function(b){b(a)})}a.cast=b}),a("promise/config",["exports"],function(a){"use strict";function b(a,b){return 2!==arguments.length?c[a]:(c[a]=b,void 0)}var c={instrument:!1};a.config=c,a.configure=b}),a("promise/polyfill",["./promise","./utils","exports"],function(a,b,c){"use strict";function d(){var a="Promise"in window&&"cast"in window.Promise&&"resolve"in window.Promise&&"reject"in window.Promise&&"all"in window.Promise&&"race"in window.Promise&&function(){var a;return new window.Promise(function(b){a=b}),f(a)}();a||(window.Promise=e)}var e=a.Promise,f=b.isFunction;c.polyfill=d}),a("promise/promise",["./config","./utils","./cast","./all","./race","./resolve","./reject","./asap","exports"],function(a,b,c,d,e,f,g,h,i){"use strict";function j(a){if(!w(a))throw new TypeError("You must pass a resolver function as the first argument to the promise constructor");if(!(this instanceof j))throw new TypeError("Failed to construct 'Promise': Please use the 'new' operator, this object constructor cannot be called as a function.");this._subscribers=[],k(a,this)}function k(a,b){function c(a){p(b,a)}function d(a){r(b,a)}try{a(c,d)}catch(e){d(e)}}function l(a,b,c,d){var e,f,g,h,i=w(c);if(i)try{e=c(d),g=!0}catch(j){h=!0,f=j}else e=d,g=!0;o(b,e)||(i&&g?p(b,e):h?r(b,f):a===F?p(b,e):a===G&&r(b,e))}function m(a,b,c,d){var e=a._subscribers,f=e.length;e[f]=b,e[f+F]=c,e[f+G]=d}function n(a,b){for(var c,d,e=a._subscribers,f=a._detail,g=0;g<e.length;g+=3)c=e[g],d=e[g+b],l(b,c,d,f);a._subscribers=null}function o(a,b){var c,d=null;try{if(a===b)throw new TypeError("A promises callback cannot return that same promise.");if(v(b)&&(d=b.then,w(d)))return d.call(b,function(d){return c?!0:(c=!0,b!==d?p(a,d):q(a,d),void 0)},function(b){return c?!0:(c=!0,r(a,b),void 0)}),!0}catch(e){return c?!0:(r(a,e),!0)}return!1}function p(a,b){a===b?q(a,b):o(a,b)||q(a,b)}function q(a,b){a._state===D&&(a._state=E,a._detail=b,u.async(s,a))}function r(a,b){a._state===D&&(a._state=E,a._detail=b,u.async(t,a))}function s(a){n(a,a._state=F)}function t(a){n(a,a._state=G)}var u=a.config,v=(a.configure,b.objectOrFunction),w=b.isFunction,x=(b.now,c.cast),y=d.all,z=e.race,A=f.resolve,B=g.reject,C=h.asap;u.async=C;var D=void 0,E=0,F=1,G=2;j.prototype={constructor:j,_state:void 0,_detail:void 0,_subscribers:void 0,then:function(a,b){var c=this,d=new this.constructor(function(){});if(this._state){var e=arguments;u.async(function(){l(c._state,d,e[c._state-1],c._detail)})}else m(this,d,a,b);return d},"catch":function(a){return this.then(null,a)}},j.all=y,j.cast=x,j.race=z,j.resolve=A,j.reject=B,i.Promise=j}),a("promise/race",["./utils","exports"],function(a,b){"use strict";function c(a){var b=this;if(!d(a))throw new TypeError("You must pass an array to race.");return new b(function(b,c){for(var d,e=0;e<a.length;e++)d=a[e],d&&"function"==typeof d.then?d.then(b,c):b(d)})}var d=a.isArray;b.race=c}),a("promise/reject",["exports"],function(a){"use strict";function b(a){var b=this;return new b(function(b,c){c(a)})}a.reject=b}),a("promise/resolve",["exports"],function(a){"use strict";function b(a){var b=this;return new b(function(b){b(a)})}a.resolve=b}),a("promise/utils",["exports"],function(a){"use strict";function b(a){return c(a)||"object"==typeof a&&null!==a}function c(a){return"function"==typeof a}function d(a){return"[object Array]"===Object.prototype.toString.call(a)}var e=Date.now||function(){return(new Date).getTime()};a.objectOrFunction=b,a.isFunction=c,a.isArray=d,a.now=e}),b("promise/polyfill").polyfill()}();

  if (typeof(Object.create) !== "function" || typeof(document.createElement("canvas").getContext) !== "function") {
      window.html2canvas = function() {
          return Promise.reject("No canvas support");
      };
      return;
  }

  /*! https://mths.be/punycode v1.3.1 by @mathias */
  ;(function(root) {

  	/** Detect free variables */
  	var freeExports = typeof exports == 'object' && exports &&
  		!exports.nodeType && exports;
  	var freeModule = typeof module == 'object' && module &&
  		!module.nodeType && module;
  	var freeGlobal = typeof global == 'object' && global;
  	if (
  		freeGlobal.global === freeGlobal ||
  		freeGlobal.window === freeGlobal ||
  		freeGlobal.self === freeGlobal
  	) {
  		root = freeGlobal;
  	}

  	/**
  	 * The `punycode` object.
  	 * @name punycode
  	 * @type Object
  	 */
  	var punycode,

  	/** Highest positive signed 32-bit float value */
  	maxInt = 2147483647, // aka. 0x7FFFFFFF or 2^31-1

  	/** Bootstring parameters */
  	base = 36,
  	tMin = 1,
  	tMax = 26,
  	skew = 38,
  	damp = 700,
  	initialBias = 72,
  	initialN = 128, // 0x80
  	delimiter = '-', // '\x2D'

  	/** Regular expressions */
  	regexPunycode = /^xn--/,
  	regexNonASCII = /[^\x20-\x7E]/, // unprintable ASCII chars + non-ASCII chars
  	regexSeparators = /[\x2E\u3002\uFF0E\uFF61]/g, // RFC 3490 separators

  	/** Error messages */
  	errors = {
  		'overflow': 'Overflow: input needs wider integers to process',
  		'not-basic': 'Illegal input >= 0x80 (not a basic code point)',
  		'invalid-input': 'Invalid input'
  	},

  	/** Convenience shortcuts */
  	baseMinusTMin = base - tMin,
  	floor = Math.floor,
  	stringFromCharCode = String.fromCharCode,

  	/** Temporary variable */
  	key;

  	/*--------------------------------------------------------------------------*/

  	/**
  	 * A generic error utility function.
  	 * @private
  	 * @param {String} type The error type.
  	 * @returns {Error} Throws a `RangeError` with the applicable error message.
  	 */
  	function error(type) {
  		throw RangeError(errors[type]);
  	}

  	/**
  	 * A generic `Array#map` utility function.
  	 * @private
  	 * @param {Array} array The array to iterate over.
  	 * @param {Function} callback The function that gets called for every array
  	 * item.
  	 * @returns {Array} A new array of values returned by the callback function.
  	 */
  	function map(array, fn) {
  		var length = array.length;
  		var result = [];
  		while (length--) {
  			result[length] = fn(array[length]);
  		}
  		return result;
  	}

  	/**
  	 * A simple `Array#map`-like wrapper to work with domain name strings or email
  	 * addresses.
  	 * @private
  	 * @param {String} domain The domain name or email address.
  	 * @param {Function} callback The function that gets called for every
  	 * character.
  	 * @returns {Array} A new string of characters returned by the callback
  	 * function.
  	 */
  	function mapDomain(string, fn) {
  		var parts = string.split('@');
  		var result = '';
  		if (parts.length > 1) {
  			// In email addresses, only the domain name should be punycoded. Leave
  			// the local part (i.e. everything up to `@`) intact.
  			result = parts[0] + '@';
  			string = parts[1];
  		}
  		var labels = string.split(regexSeparators);
  		var encoded = map(labels, fn).join('.');
  		return result + encoded;
  	}

  	/**
  	 * Creates an array containing the numeric code points of each Unicode
  	 * character in the string. While JavaScript uses UCS-2 internally,
  	 * this function will convert a pair of surrogate halves (each of which
  	 * UCS-2 exposes as separate characters) into a single code point,
  	 * matching UTF-16.
  	 * @see `punycode.ucs2.encode`
  	 * @see <https://mathiasbynens.be/notes/javascript-encoding>
  	 * @memberOf punycode.ucs2
  	 * @name decode
  	 * @param {String} string The Unicode input string (UCS-2).
  	 * @returns {Array} The new array of code points.
  	 */
  	function ucs2decode(string) {
  		var output = [],
  		    counter = 0,
  		    length = string.length,
  		    value,
  		    extra;
  		while (counter < length) {
  			value = string.charCodeAt(counter++);
  			if (value >= 0xD800 && value <= 0xDBFF && counter < length) {
  				// high surrogate, and there is a next character
  				extra = string.charCodeAt(counter++);
  				if ((extra & 0xFC00) == 0xDC00) { // low surrogate
  					output.push(((value & 0x3FF) << 10) + (extra & 0x3FF) + 0x10000);
  				} else {
  					// unmatched surrogate; only append this code unit, in case the next
  					// code unit is the high surrogate of a surrogate pair
  					output.push(value);
  					counter--;
  				}
  			} else {
  				output.push(value);
  			}
  		}
  		return output;
  	}

  	/**
  	 * Creates a string based on an array of numeric code points.
  	 * @see `punycode.ucs2.decode`
  	 * @memberOf punycode.ucs2
  	 * @name encode
  	 * @param {Array} codePoints The array of numeric code points.
  	 * @returns {String} The new Unicode string (UCS-2).
  	 */
  	function ucs2encode(array) {
  		return map(array, function(value) {
  			var output = '';
  			if (value > 0xFFFF) {
  				value -= 0x10000;
  				output += stringFromCharCode(value >>> 10 & 0x3FF | 0xD800);
  				value = 0xDC00 | value & 0x3FF;
  			}
  			output += stringFromCharCode(value);
  			return output;
  		}).join('');
  	}

  	/**
  	 * Converts a basic code point into a digit/integer.
  	 * @see `digitToBasic()`
  	 * @private
  	 * @param {Number} codePoint The basic numeric code point value.
  	 * @returns {Number} The numeric value of a basic code point (for use in
  	 * representing integers) in the range `0` to `base - 1`, or `base` if
  	 * the code point does not represent a value.
  	 */
  	function basicToDigit(codePoint) {
  		if (codePoint - 48 < 10) {
  			return codePoint - 22;
  		}
  		if (codePoint - 65 < 26) {
  			return codePoint - 65;
  		}
  		if (codePoint - 97 < 26) {
  			return codePoint - 97;
  		}
  		return base;
  	}

  	/**
  	 * Converts a digit/integer into a basic code point.
  	 * @see `basicToDigit()`
  	 * @private
  	 * @param {Number} digit The numeric value of a basic code point.
  	 * @returns {Number} The basic code point whose value (when used for
  	 * representing integers) is `digit`, which needs to be in the range
  	 * `0` to `base - 1`. If `flag` is non-zero, the uppercase form is
  	 * used; else, the lowercase form is used. The behavior is undefined
  	 * if `flag` is non-zero and `digit` has no uppercase form.
  	 */
  	function digitToBasic(digit, flag) {
  		//  0..25 map to ASCII a..z or A..Z
  		// 26..35 map to ASCII 0..9
  		return digit + 22 + 75 * (digit < 26) - ((flag != 0) << 5);
  	}

  	/**
  	 * Bias adaptation function as per section 3.4 of RFC 3492.
  	 * http://tools.ietf.org/html/rfc3492#section-3.4
  	 * @private
  	 */
  	function adapt(delta, numPoints, firstTime) {
  		var k = 0;
  		delta = firstTime ? floor(delta / damp) : delta >> 1;
  		delta += floor(delta / numPoints);
  		for (/* no initialization */; delta > baseMinusTMin * tMax >> 1; k += base) {
  			delta = floor(delta / baseMinusTMin);
  		}
  		return floor(k + (baseMinusTMin + 1) * delta / (delta + skew));
  	}

  	/**
  	 * Converts a Punycode string of ASCII-only symbols to a string of Unicode
  	 * symbols.
  	 * @memberOf punycode
  	 * @param {String} input The Punycode string of ASCII-only symbols.
  	 * @returns {String} The resulting string of Unicode symbols.
  	 */
  	function decode(input) {
  		// Don't use UCS-2
  		var output = [],
  		    inputLength = input.length,
  		    out,
  		    i = 0,
  		    n = initialN,
  		    bias = initialBias,
  		    basic,
  		    j,
  		    index,
  		    oldi,
  		    w,
  		    k,
  		    digit,
  		    t,
  		    /** Cached calculation results */
  		    baseMinusT;

  		// Handle the basic code points: let `basic` be the number of input code
  		// points before the last delimiter, or `0` if there is none, then copy
  		// the first basic code points to the output.

  		basic = input.lastIndexOf(delimiter);
  		if (basic < 0) {
  			basic = 0;
  		}

  		for (j = 0; j < basic; ++j) {
  			// if it's not a basic code point
  			if (input.charCodeAt(j) >= 0x80) {
  				error('not-basic');
  			}
  			output.push(input.charCodeAt(j));
  		}

  		// Main decoding loop: start just after the last delimiter if any basic code
  		// points were copied; start at the beginning otherwise.

  		for (index = basic > 0 ? basic + 1 : 0; index < inputLength; /* no final expression */) {

  			// `index` is the index of the next character to be consumed.
  			// Decode a generalized variable-length integer into `delta`,
  			// which gets added to `i`. The overflow checking is easier
  			// if we increase `i` as we go, then subtract off its starting
  			// value at the end to obtain `delta`.
  			for (oldi = i, w = 1, k = base; /* no condition */; k += base) {

  				if (index >= inputLength) {
  					error('invalid-input');
  				}

  				digit = basicToDigit(input.charCodeAt(index++));

  				if (digit >= base || digit > floor((maxInt - i) / w)) {
  					error('overflow');
  				}

  				i += digit * w;
  				t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);

  				if (digit < t) {
  					break;
  				}

  				baseMinusT = base - t;
  				if (w > floor(maxInt / baseMinusT)) {
  					error('overflow');
  				}

  				w *= baseMinusT;

  			}

  			out = output.length + 1;
  			bias = adapt(i - oldi, out, oldi == 0);

  			// `i` was supposed to wrap around from `out` to `0`,
  			// incrementing `n` each time, so we'll fix that now:
  			if (floor(i / out) > maxInt - n) {
  				error('overflow');
  			}

  			n += floor(i / out);
  			i %= out;

  			// Insert `n` at position `i` of the output
  			output.splice(i++, 0, n);

  		}

  		return ucs2encode(output);
  	}

  	/**
  	 * Converts a string of Unicode symbols (e.g. a domain name label) to a
  	 * Punycode string of ASCII-only symbols.
  	 * @memberOf punycode
  	 * @param {String} input The string of Unicode symbols.
  	 * @returns {String} The resulting Punycode string of ASCII-only symbols.
  	 */
  	function encode(input) {
  		var n,
  		    delta,
  		    handledCPCount,
  		    basicLength,
  		    bias,
  		    j,
  		    m,
  		    q,
  		    k,
  		    t,
  		    currentValue,
  		    output = [],
  		    /** `inputLength` will hold the number of code points in `input`. */
  		    inputLength,
  		    /** Cached calculation results */
  		    handledCPCountPlusOne,
  		    baseMinusT,
  		    qMinusT;

  		// Convert the input in UCS-2 to Unicode
  		input = ucs2decode(input);

  		// Cache the length
  		inputLength = input.length;

  		// Initialize the state
  		n = initialN;
  		delta = 0;
  		bias = initialBias;

  		// Handle the basic code points
  		for (j = 0; j < inputLength; ++j) {
  			currentValue = input[j];
  			if (currentValue < 0x80) {
  				output.push(stringFromCharCode(currentValue));
  			}
  		}

  		handledCPCount = basicLength = output.length;

  		// `handledCPCount` is the number of code points that have been handled;
  		// `basicLength` is the number of basic code points.

  		// Finish the basic string - if it is not empty - with a delimiter
  		if (basicLength) {
  			output.push(delimiter);
  		}

  		// Main encoding loop:
  		while (handledCPCount < inputLength) {

  			// All non-basic code points < n have been handled already. Find the next
  			// larger one:
  			for (m = maxInt, j = 0; j < inputLength; ++j) {
  				currentValue = input[j];
  				if (currentValue >= n && currentValue < m) {
  					m = currentValue;
  				}
  			}

  			// Increase `delta` enough to advance the decoder's <n,i> state to <m,0>,
  			// but guard against overflow
  			handledCPCountPlusOne = handledCPCount + 1;
  			if (m - n > floor((maxInt - delta) / handledCPCountPlusOne)) {
  				error('overflow');
  			}

  			delta += (m - n) * handledCPCountPlusOne;
  			n = m;

  			for (j = 0; j < inputLength; ++j) {
  				currentValue = input[j];

  				if (currentValue < n && ++delta > maxInt) {
  					error('overflow');
  				}

  				if (currentValue == n) {
  					// Represent delta as a generalized variable-length integer
  					for (q = delta, k = base; /* no condition */; k += base) {
  						t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);
  						if (q < t) {
  							break;
  						}
  						qMinusT = q - t;
  						baseMinusT = base - t;
  						output.push(
  							stringFromCharCode(digitToBasic(t + qMinusT % baseMinusT, 0))
  						);
  						q = floor(qMinusT / baseMinusT);
  					}

  					output.push(stringFromCharCode(digitToBasic(q, 0)));
  					bias = adapt(delta, handledCPCountPlusOne, handledCPCount == basicLength);
  					delta = 0;
  					++handledCPCount;
  				}
  			}

  			++delta;
  			++n;

  		}
  		return output.join('');
  	}

  	/**
  	 * Converts a Punycode string representing a domain name or an email address
  	 * to Unicode. Only the Punycoded parts of the input will be converted, i.e.
  	 * it doesn't matter if you call it on a string that has already been
  	 * converted to Unicode.
  	 * @memberOf punycode
  	 * @param {String} input The Punycoded domain name or email address to
  	 * convert to Unicode.
  	 * @returns {String} The Unicode representation of the given Punycode
  	 * string.
  	 */
  	function toUnicode(input) {
  		return mapDomain(input, function(string) {
  			return regexPunycode.test(string)
  				? decode(string.slice(4).toLowerCase())
  				: string;
  		});
  	}

  	/**
  	 * Converts a Unicode string representing a domain name or an email address to
  	 * Punycode. Only the non-ASCII parts of the domain name will be converted,
  	 * i.e. it doesn't matter if you call it with a domain that's already in
  	 * ASCII.
  	 * @memberOf punycode
  	 * @param {String} input The domain name or email address to convert, as a
  	 * Unicode string.
  	 * @returns {String} The Punycode representation of the given domain name or
  	 * email address.
  	 */
  	function toASCII(input) {
  		return mapDomain(input, function(string) {
  			return regexNonASCII.test(string)
  				? 'xn--' + encode(string)
  				: string;
  		});
  	}

  	/*--------------------------------------------------------------------------*/

  	/** Define the public API */
  	punycode = {
  		/**
  		 * A string representing the current Punycode.js version number.
  		 * @memberOf punycode
  		 * @type String
  		 */
  		'version': '1.3.1',
  		/**
  		 * An object of methods to convert from JavaScript's internal character
  		 * representation (UCS-2) to Unicode code points, and back.
  		 * @see <https://mathiasbynens.be/notes/javascript-encoding>
  		 * @memberOf punycode
  		 * @type Object
  		 */
  		'ucs2': {
  			'decode': ucs2decode,
  			'encode': ucs2encode
  		},
  		'decode': decode,
  		'encode': encode,
  		'toASCII': toASCII,
  		'toUnicode': toUnicode
  	};

  	/** Expose `punycode` */
  	// Some AMD build optimizers, like r.js, check for specific condition patterns
  	// like the following:
  	if (
  		typeof define == 'function' &&
  		typeof define.amd == 'object' &&
  		define.amd
  	) {
  		define('punycode', function() {
  			return punycode;
  		});
  	} else if (freeExports && freeModule) {
  		if (module.exports == freeExports) { // in Node.js or RingoJS v0.8.0+
  			freeModule.exports = punycode;
  		} else { // in Narwhal or RingoJS v0.7.0-
  			for (key in punycode) {
  				punycode.hasOwnProperty(key) && (freeExports[key] = punycode[key]);
  			}
  		}
  	} else { // in Rhino or a web browser
  		root.punycode = punycode;
  	}

  }(this));

  var html2canvasNodeAttribute = "data-html2canvas-node";
  var html2canvasCanvasCloneAttribute = "data-html2canvas-canvas-clone";
  var html2canvasCanvasCloneIndex = 0;

  window.html2canvas = function(nodeList, options) {
      options = options || {};
      if (options.logging) {
          window.html2canvas.logging = true;
          window.html2canvas.start = Date.now();
      }

      options.async = typeof(options.async) === "undefined" ? true : options.async;
      options.allowTaint = typeof(options.allowTaint) === "undefined" ? false : options.allowTaint;
      options.removeContainer = typeof(options.removeContainer) === "undefined" ? true : options.removeContainer;
      options.javascriptEnabled = typeof(options.javascriptEnabled) === "undefined" ? false : options.javascriptEnabled;
      options.imageTimeout = typeof(options.imageTimeout) === "undefined" ? 10000 : options.imageTimeout;

      if (typeof(nodeList) === "string") {
          if (typeof(options.proxy) !== "string") {
              return Promise.reject("Proxy must be used when rendering url");
          }
          return loadUrlDocument(absoluteUrl(nodeList), options.proxy, document, window.innerWidth, window.innerHeight, options).then(function(container) {
              return renderWindow(container.contentWindow.document.documentElement, container, options, window.innerWidth, window.innerHeight);
          });
      }

      var node = ((nodeList === undefined) ? [document.documentElement] : ((nodeList.length) ? nodeList : [nodeList]))[0];
      node.setAttribute(html2canvasNodeAttribute, "true");
      return renderDocument(node.ownerDocument, options, node.ownerDocument.defaultView.innerWidth, node.ownerDocument.defaultView.innerHeight).then(function(canvas) {
          if (typeof(options.onrendered) === "function") {
              log("options.onrendered is deprecated, html2canvas returns a Promise containing the canvas");
              options.onrendered(canvas);
          }
          return canvas;
      });
  };

  window.html2canvas.punycode = this.punycode;
  window.html2canvas.proxy = {};

  function renderDocument(document, options, windowWidth, windowHeight) {
      return createWindowClone(document, document, windowWidth, windowHeight, options).then(function(container) {
          log("Document cloned");
          var selector = "[" + html2canvasNodeAttribute + "='true']";
          document.querySelector(selector).removeAttribute(html2canvasNodeAttribute);
          var clonedWindow = container.contentWindow;
          var node = clonedWindow.document.querySelector(selector);
          var oncloneHandler = (typeof(options.onclone) === "function") ? Promise.resolve(options.onclone(clonedWindow.document)) : Promise.resolve(true);
          return oncloneHandler.then(function() {
              return renderWindow(node, container, options, windowWidth, windowHeight);
          });
      });
  }

  function renderWindow(node, container, options, windowWidth, windowHeight) {
      var clonedWindow = container.contentWindow;
      var support = new Support(clonedWindow.document);
      var imageLoader = new ImageLoader(options, support);
      var bounds = getBounds(node);
      var width = options.type === "view" ? windowWidth : documentWidth(clonedWindow.document);
      var height = options.type === "view" ? windowHeight : documentHeight(clonedWindow.document);
      var renderer = new CanvasRenderer(width, height, imageLoader, options, document);
      var parser = new NodeParser(node, renderer, support, imageLoader, options);
      return parser.ready.then(function() {
          log("Finished rendering");
          var canvas;

          if (options.type === "view") {
              canvas = crop(renderer.canvas, {width: renderer.canvas.width, height: renderer.canvas.height, top: 0, left: 0, x: 0, y: 0});
          } else if (node === clonedWindow.document.body || node === clonedWindow.document.documentElement || options.canvas != null) {
              canvas = renderer.canvas;
          } else {
              canvas = crop(renderer.canvas, {width:  options.width != null ? options.width : bounds.width, height: options.height != null ? options.height : bounds.height, top: bounds.top, left: bounds.left, x: clonedWindow.pageXOffset, y: clonedWindow.pageYOffset});
          }

          cleanupContainer(container, options);
          return canvas;
      });
  }

  function cleanupContainer(container, options) {
      if (options.removeContainer) {
          container.parentNode.removeChild(container);
          log("Cleaned up container");
      }
  }

  function crop(canvas, bounds) {
      var croppedCanvas = document.createElement("canvas");
      var x1 = Math.min(canvas.width - 1, Math.max(0, bounds.left));
      var x2 = Math.min(canvas.width, Math.max(1, bounds.left + bounds.width));
      var y1 = Math.min(canvas.height - 1, Math.max(0, bounds.top));
      var y2 = Math.min(canvas.height, Math.max(1, bounds.top + bounds.height));
      croppedCanvas.width = bounds.width;
      croppedCanvas.height =  bounds.height;
      log("Cropping canvas at:", "left:", bounds.left, "top:", bounds.top, "width:", (x2-x1), "height:", (y2-y1));
      log("Resulting crop with width", bounds.width, "and height", bounds.height, " with x", x1, "and y", y1);
      croppedCanvas.getContext("2d").drawImage(canvas, x1, y1, x2-x1, y2-y1, bounds.x, bounds.y, x2-x1, y2-y1);
      return croppedCanvas;
  }

  function documentWidth (doc) {
      return Math.max(
          Math.max(doc.body.scrollWidth, doc.documentElement.scrollWidth),
          Math.max(doc.body.offsetWidth, doc.documentElement.offsetWidth),
          Math.max(doc.body.clientWidth, doc.documentElement.clientWidth)
      );
  }

  function documentHeight (doc) {
      return Math.max(
          Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight),
          Math.max(doc.body.offsetHeight, doc.documentElement.offsetHeight),
          Math.max(doc.body.clientHeight, doc.documentElement.clientHeight)
      );
  }

  function smallImage() {
      return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
  }

  function createWindowClone(ownerDocument, containerDocument, width, height, options) {
      labelCanvasElements(ownerDocument);
      var documentElement = ownerDocument.documentElement.cloneNode(true),
          container = containerDocument.createElement("iframe");

      container.className = "html2canvas-container";
      container.style.visibility = "hidden";
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0px";
      container.style.border = "0";
      container.width = width;
      container.height = height;
      container.scrolling = "no"; // ios won't scroll without it
      containerDocument.body.appendChild(container);

      return new Promise(function(resolve) {
          var documentClone = container.contentWindow.document;
          /* Chrome doesn't detect relative background-images assigned in inline <style> sheets when fetched through getComputedStyle
          if window url is about:blank, we can assign the url to current by writing onto the document
           */
          container.contentWindow.onload = container.onload = function() {
              var interval = setInterval(function() {
                  if (documentClone.body.childNodes.length > 0) {
                      cloneCanvasContents(ownerDocument, documentClone);
                      clearInterval(interval);
                      if (options.type === "view") {
                          container.contentWindow.scrollTo(x, y);
                      }
                      resolve(container);
                  }
              }, 50);
          };

          var x = ownerDocument.defaultView.pageXOffset;
          var y = ownerDocument.defaultView.pageYOffset;

          documentClone.open();
          documentClone.write("<!DOCTYPE html><html></html>");
          // Chrome scrolls the parent document for some reason after the write to the cloned window???
          restoreOwnerScroll(ownerDocument, x, y);
          documentClone.replaceChild(options.javascriptEnabled === true ? documentClone.adoptNode(documentElement) : removeScriptNodes(documentClone.adoptNode(documentElement)), documentClone.documentElement);
          documentClone.close();
      });
  }

  function restoreOwnerScroll(ownerDocument, x, y) {
      if (x !== ownerDocument.defaultView.pageXOffset || y !== ownerDocument.defaultView.pageYOffset) {
          ownerDocument.defaultView.scrollTo(x, y);
      }
  }

  function loadUrlDocument(src, proxy, document, width, height, options) {
      return new Proxy(src, proxy, window.document).then(documentFromHTML(src)).then(function(doc) {
          return createWindowClone(doc, document, width, height, options);
      });
  }

  function documentFromHTML(src) {
      return function(html) {
          var parser = new DOMParser(), doc;
          try {
              doc = parser.parseFromString(html, "text/html");
          } catch(e) {
              log("DOMParser not supported, falling back to createHTMLDocument");
              doc = document.implementation.createHTMLDocument("");
              try {
                  doc.open();
                  doc.write(html);
                  doc.close();
              } catch(ee) {
                  log("createHTMLDocument write not supported, falling back to document.body.innerHTML");
                  doc.body.innerHTML = html; // ie9 doesnt support writing to documentElement
              }
          }

          var b = doc.querySelector("base");
          if (!b || !b.href.host) {
              var base = doc.createElement("base");
              base.href = src;
              doc.head.insertBefore(base, doc.head.firstChild);
          }

          return doc;
      };
  }


  function labelCanvasElements(ownerDocument) {
      [].slice.call(ownerDocument.querySelectorAll("canvas"), 0).forEach(function(canvas) {
          canvas.setAttribute(html2canvasCanvasCloneAttribute, "canvas-" + html2canvasCanvasCloneIndex++);
      });
  }

  function cloneCanvasContents(ownerDocument, documentClone) {
      [].slice.call(ownerDocument.querySelectorAll("[" + html2canvasCanvasCloneAttribute + "]"), 0).forEach(function(canvas) {
          try {
              var clonedCanvas = documentClone.querySelector('[' + html2canvasCanvasCloneAttribute + '="' + canvas.getAttribute(html2canvasCanvasCloneAttribute) + '"]');
              if (clonedCanvas) {
                  clonedCanvas.width = canvas.width;
                  clonedCanvas.height = canvas.height;
                  clonedCanvas.getContext("2d").putImageData(canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height), 0, 0);
              }
          } catch(e) {
              log("Unable to copy canvas content from", canvas, e);
          }
          canvas.removeAttribute(html2canvasCanvasCloneAttribute);
      });
  }

  function removeScriptNodes(parent) {
      [].slice.call(parent.childNodes, 0).filter(isElementNode).forEach(function(node) {
          if (node.tagName === "SCRIPT") {
              parent.removeChild(node);
          } else {
              removeScriptNodes(node);
          }
      });
      return parent;
  }

  function isElementNode(node) {
      return node.nodeType === Node.ELEMENT_NODE;
  }

  function absoluteUrl(url) {
      var link = document.createElement("a");
      link.href = url;
      link.href = link.href;
      return link;
  }

  function DummyImageContainer(src) {
      this.src = src;
      log("DummyImageContainer for", src);
      if (!this.promise || !this.image) {
          log("Initiating DummyImageContainer");
          DummyImageContainer.prototype.image = new Image();
          var image = this.image;
          DummyImageContainer.prototype.promise = new Promise(function(resolve, reject) {
              image.onload = resolve;
              image.onerror = reject;
              image.src = smallImage();
              if (image.complete === true) {
                  resolve(image);
              }
          });
      }
  }

  function Font(family, size) {
      var container = document.createElement('div'),
          img = document.createElement('img'),
          span = document.createElement('span'),
          sampleText = 'Hidden Text',
          baseline,
          middle;

      container.style.visibility = "hidden";
      container.style.fontFamily = family;
      container.style.fontSize = size;
      container.style.margin = 0;
      container.style.padding = 0;

      document.body.appendChild(container);

      img.src = smallImage();
      img.width = 1;
      img.height = 1;

      img.style.margin = 0;
      img.style.padding = 0;
      img.style.verticalAlign = "baseline";

      span.style.fontFamily = family;
      span.style.fontSize = size;
      span.style.margin = 0;
      span.style.padding = 0;

      span.appendChild(document.createTextNode(sampleText));
      container.appendChild(span);
      container.appendChild(img);
      baseline = (img.offsetTop - span.offsetTop) + 1;

      container.removeChild(span);
      container.appendChild(document.createTextNode(sampleText));

      container.style.lineHeight = "normal";
      img.style.verticalAlign = "super";

      middle = (img.offsetTop-container.offsetTop) + 1;

      document.body.removeChild(container);

      this.baseline = baseline;
      this.lineWidth = 1;
      this.middle = middle;
  }

  function FontMetrics() {
      this.data = {};
  }

  FontMetrics.prototype.getMetrics = function(family, size) {
      if (this.data[family + "-" + size] === undefined) {
          this.data[family + "-" + size] = new Font(family, size);
      }
      return this.data[family + "-" + size];
  };

  function FrameContainer(container, sameOrigin, options) {
      this.image = null;
      this.src = container;
      var self = this;
      var bounds = getBounds(container);
      this.promise = (!sameOrigin ? this.proxyLoad(options.proxy, bounds, options) : new Promise(function(resolve) {
          if (container.contentWindow.document.URL === "about:blank" || container.contentWindow.document.documentElement == null) {
              container.contentWindow.onload = container.onload = function() {
                  resolve(container);
              };
          } else {
              resolve(container);
          }
      })).then(function(container) {
          return html2canvas(container.contentWindow.document.documentElement, {type: 'view', width: container.width, height: container.height, proxy: options.proxy, javascriptEnabled: options.javascriptEnabled, removeContainer: options.removeContainer, allowTaint: options.allowTaint, imageTimeout: options.imageTimeout / 2});
      }).then(function(canvas) {
          return self.image = canvas;
      });
  }

  FrameContainer.prototype.proxyLoad = function(proxy, bounds, options) {
      var container = this.src;
      return loadUrlDocument(container.src, proxy, container.ownerDocument, bounds.width, bounds.height, options);
  };

  function GradientContainer(imageData) {
      this.src = imageData.value;
      this.colorStops = [];
      this.type = null;
      this.x0 = 0.5;
      this.y0 = 0.5;
      this.x1 = 0.5;
      this.y1 = 0.5;
      this.promise = Promise.resolve(true);
  }

  GradientContainer.prototype.TYPES = {
      LINEAR: 1,
      RADIAL: 2
  };

  function ImageContainer(src, cors) {
      this.src = src;
      this.image = new Image();
      var self = this;
      this.tainted = null;
      this.promise = new Promise(function(resolve, reject) {
          self.image.onload = resolve;
          self.image.onerror = reject;
          if (cors) {
              self.image.crossOrigin = "anonymous";
          }
          self.image.src = src;
          if (self.image.complete === true) {
              resolve(self.image);
          }
      });
  }

  function ImageLoader(options, support) {
      this.link = null;
      this.options = options;
      this.support = support;
      this.origin = this.getOrigin(window.location.href);
  }

  ImageLoader.prototype.findImages = function(nodes) {
      var images = [];
      nodes.reduce(function(imageNodes, container) {
          switch(container.node.nodeName) {
          case "IMG":
              return imageNodes.concat([{
                  args: [container.node.src],
                  method: "url"
              }]);
          case "svg":
          case "IFRAME":
              return imageNodes.concat([{
                  args: [container.node],
                  method: container.node.nodeName
              }]);
          }
          return imageNodes;
      }, []).forEach(this.addImage(images, this.loadImage), this);
      return images;
  };

  ImageLoader.prototype.findBackgroundImage = function(images, container) {
      container.parseBackgroundImages().filter(this.hasImageBackground).forEach(this.addImage(images, this.loadImage), this);
      return images;
  };

  ImageLoader.prototype.addImage = function(images, callback) {
      return function(newImage) {
          newImage.args.forEach(function(image) {
              if (!this.imageExists(images, image)) {
                  images.splice(0, 0, callback.call(this, newImage));
                  log('Added image #' + (images.length), typeof(image) === "string" ? image.substring(0, 100) : image);
              }
          }, this);
      };
  };

  ImageLoader.prototype.hasImageBackground = function(imageData) {
      return imageData.method !== "none";
  };

  ImageLoader.prototype.loadImage = function(imageData) {
      if (imageData.method === "url") {
          var src = imageData.args[0];
          if (this.isSVG(src) && !this.support.svg && !this.options.allowTaint) {
              return new SVGContainer(src);
          } else if (src.match(/data:image\/.*;base64,/i)) {
              return new ImageContainer(src.replace(/url\(['"]{0,}|['"]{0,}\)$/ig, ''), false);
          } else if (this.isSameOrigin(src) || this.options.allowTaint === true || this.isSVG(src)) {
              return new ImageContainer(src, false);
          } else if (this.support.cors && !this.options.allowTaint && this.options.useCORS) {
              return new ImageContainer(src, true);
          } else if (this.options.proxy) {
              return new ProxyImageContainer(src, this.options.proxy);
          } else {
              return new DummyImageContainer(src);
          }
      } else if (imageData.method === "linear-gradient") {
          return new LinearGradientContainer(imageData);
      } else if (imageData.method === "gradient") {
          return new WebkitGradientContainer(imageData);
      } else if (imageData.method === "svg") {
          return new SVGNodeContainer(imageData.args[0], this.support.svg);
      } else if (imageData.method === "IFRAME") {
          return new FrameContainer(imageData.args[0], this.isSameOrigin(imageData.args[0].src), this.options);
      } else {
          return new DummyImageContainer(imageData);
      }
  };

  ImageLoader.prototype.isSVG = function(src) {
      return src.substring(src.length - 3).toLowerCase() === "svg" || SVGContainer.prototype.isInline(src);
  };

  ImageLoader.prototype.imageExists = function(images, src) {
      return images.some(function(image) {
          return image.src === src;
      });
  };

  ImageLoader.prototype.isSameOrigin = function(url) {
      return (this.getOrigin(url) === this.origin);
  };

  ImageLoader.prototype.getOrigin = function(url) {
      var link = this.link || (this.link = document.createElement("a"));
      link.href = url;
      link.href = link.href; // IE9, LOL! - http://jsfiddle.net/niklasvh/2e48b/
      return link.protocol + link.hostname + link.port;
  };

  ImageLoader.prototype.getPromise = function(container) {
      return this.timeout(container, this.options.imageTimeout)['catch'](function() {
          var dummy = new DummyImageContainer(container.src);
          return dummy.promise.then(function(image) {
              container.image = image;
          });
      });
  };

  ImageLoader.prototype.get = function(src) {
      var found = null;
      return this.images.some(function(img) {
          return (found = img).src === src;
      }) ? found : null;
  };

  ImageLoader.prototype.fetch = function(nodes) {
      this.images = nodes.reduce(bind(this.findBackgroundImage, this), this.findImages(nodes));
      this.images.forEach(function(image, index) {
          image.promise.then(function() {
              log("Succesfully loaded image #"+ (index+1), image);
          }, function(e) {
              log("Failed loading image #"+ (index+1), image, e);
          });
      });
      this.ready = Promise.all(this.images.map(this.getPromise, this));
      log("Finished searching images");
      return this;
  };

  ImageLoader.prototype.timeout = function(container, timeout) {
      var timer;
      return Promise.race([container.promise, new Promise(function(res, reject) {
          timer = setTimeout(function() {
              log("Timed out loading image", container);
              reject(container);
          }, timeout);
      })]).then(function(container) {
          clearTimeout(timer);
          return container;
      });
  };

  function LinearGradientContainer(imageData) {
      GradientContainer.apply(this, arguments);
      this.type = this.TYPES.LINEAR;

      var hasDirection = imageData.args[0].match(this.stepRegExp) === null;

      if (hasDirection) {
          imageData.args[0].split(" ").reverse().forEach(function(position) {
              switch(position) {
                  case "left":
                      this.x0 = 0;
                      this.x1 = 1;
                      break;
                  case "top":
                      this.y0 = 0;
                      this.y1 = 1;
                      break;
                  case "right":
                      this.x0 = 1;
                      this.x1 = 0;
                      break;
                  case "bottom":
                      this.y0 = 1;
                      this.y1 = 0;
                      break;
                  case "to":
                      var y0 = this.y0;
                      var x0 = this.x0;
                      this.y0 = this.y1;
                      this.x0 = this.x1;
                      this.x1 = x0;
                      this.y1 = y0;
                      break;
              }
          }, this);
      } else {
          this.y0 = 0;
          this.y1 = 1;
      }

      this.colorStops = imageData.args.slice(hasDirection ? 1 : 0).map(function(colorStop) {
          var colorStopMatch = colorStop.match(this.stepRegExp);
          return {
              color: colorStopMatch[1],
              stop: colorStopMatch[3] === "%" ? colorStopMatch[2] / 100 : null
          };
      }, this);

      if (this.colorStops[0].stop === null) {
          this.colorStops[0].stop = 0;
      }

      if (this.colorStops[this.colorStops.length - 1].stop === null) {
          this.colorStops[this.colorStops.length - 1].stop = 1;
      }

      this.colorStops.forEach(function(colorStop, index) {
          if (colorStop.stop === null) {
              this.colorStops.slice(index).some(function(find, count) {
                  if (find.stop !== null) {
                      colorStop.stop = ((find.stop - this.colorStops[index - 1].stop) / (count + 1)) + this.colorStops[index - 1].stop;
                      return true;
                  } else {
                      return false;
                  }
              }, this);
          }
      }, this);
  }

  LinearGradientContainer.prototype = Object.create(GradientContainer.prototype);

  LinearGradientContainer.prototype.stepRegExp = /((?:rgb|rgba)\(\d{1,3},\s\d{1,3},\s\d{1,3}(?:,\s[0-9\.]+)?\))\s*(\d{1,3})?(%|px)?/;

  function log() {
      if (window.html2canvas.logging && window.console && window.console.log) {
          Function.prototype.bind.call(window.console.log, (window.console)).apply(window.console, [(Date.now() - window.html2canvas.start) + "ms", "html2canvas:"].concat([].slice.call(arguments, 0)));
      }
  }

  function NodeContainer(node, parent) {
      this.node = node;
      this.parent = parent;
      this.stack = null;
      this.bounds = null;
      this.borders = null;
      this.clip = [];
      this.backgroundClip = [];
      this.offsetBounds = null;
      this.visible = null;
      this.computedStyles = null;
      this.styles = {};
      this.backgroundImages = null;
      this.transformData = null;
      this.transformMatrix = null;
      this.isPseudoElement = false;
      this.opacity = null;
  }

  NodeContainer.prototype.cloneTo = function(stack) {
      stack.visible = this.visible;
      stack.borders = this.borders;
      stack.bounds = this.bounds;
      stack.clip = this.clip;
      stack.backgroundClip = this.backgroundClip;
      stack.computedStyles = this.computedStyles;
      stack.styles = this.styles;
      stack.backgroundImages = this.backgroundImages;
      stack.opacity = this.opacity;
  };

  NodeContainer.prototype.getOpacity = function() {
      return this.opacity === null ? (this.opacity = this.cssFloat('opacity')) : this.opacity;
  };

  NodeContainer.prototype.assignStack = function(stack) {
      this.stack = stack;
      stack.children.push(this);
  };

  NodeContainer.prototype.isElementVisible = function() {
      return this.node.nodeType === Node.TEXT_NODE ? this.parent.visible : (
          this.css('display') !== "none" &&
          this.css('visibility') !== "hidden" &&
          !this.node.hasAttribute("data-html2canvas-ignore") &&
          (this.node.nodeName !== "INPUT" || this.node.getAttribute("type") !== "hidden")
      );
  };

  NodeContainer.prototype.css = function(attribute) {
      if (!this.computedStyles) {
          this.computedStyles = this.isPseudoElement ? this.parent.computedStyle(this.before ? ":before" : ":after") : this.computedStyle(null);
      }

      return this.styles[attribute] || (this.styles[attribute] = this.computedStyles[attribute]);
  };

  NodeContainer.prototype.prefixedCss = function(attribute) {
      var prefixes = ["webkit", "moz", "ms", "o"];
      var value = this.css(attribute);
      if (value === undefined) {
          prefixes.some(function(prefix) {
              value = this.css(prefix + attribute.substr(0, 1).toUpperCase() + attribute.substr(1));
              return value !== undefined;
          }, this);
      }
      return value === undefined ? null : value;
  };

  NodeContainer.prototype.computedStyle = function(type) {
      return this.node.ownerDocument.defaultView.getComputedStyle(this.node, type);
  };

  NodeContainer.prototype.cssInt = function(attribute) {
      var value = parseInt(this.css(attribute), 10);
      return (isNaN(value)) ? 0 : value; // borders in old IE are throwing 'medium' for demo.html
  };

  NodeContainer.prototype.cssFloat = function(attribute) {
      var value = parseFloat(this.css(attribute));
      return (isNaN(value)) ? 0 : value;
  };

  NodeContainer.prototype.fontWeight = function() {
      var weight = this.css("fontWeight");
      switch(parseInt(weight, 10)){
      case 401:
          weight = "bold";
          break;
      case 400:
          weight = "normal";
          break;
      }
      return weight;
  };

  NodeContainer.prototype.parseClip = function() {
      var matches = this.css('clip').match(this.CLIP);
      if (matches) {
          return {
              top: parseInt(matches[1], 10),
              right: parseInt(matches[2], 10),
              bottom: parseInt(matches[3], 10),
              left: parseInt(matches[4], 10)
          };
      }
      return null;
  };

  NodeContainer.prototype.parseBackgroundImages = function() {
      return this.backgroundImages || (this.backgroundImages = parseBackgrounds(this.css("backgroundImage")));
  };

  NodeContainer.prototype.cssList = function(property, index) {
      var value = (this.css(property) || '').split(',');
      value = value[index || 0] || value[0] || 'auto';
      value = value.trim().split(' ');
      if (value.length === 1) {
          value = [value[0], value[0]];
      }
      return value;
  };

  NodeContainer.prototype.parseBackgroundSize = function(bounds, image, index) {
      var size = this.cssList("backgroundSize", index);
      var width, height;

      if (isPercentage(size[0])) {
          width = bounds.width * parseFloat(size[0]) / 100;
      } else if (/contain|cover/.test(size[0])) {
          var targetRatio = bounds.width / bounds.height, currentRatio = image.width / image.height;
          return (targetRatio < currentRatio ^ size[0] === 'contain') ?  {width: bounds.height * currentRatio, height: bounds.height} : {width: bounds.width, height: bounds.width / currentRatio};
      } else {
          width = parseInt(size[0], 10);
      }

      if (size[0] === 'auto' && size[1] === 'auto') {
          height = image.height;
      } else if (size[1] === 'auto') {
          height = width / image.width * image.height;
      } else if (isPercentage(size[1])) {
          height =  bounds.height * parseFloat(size[1]) / 100;
      } else {
          height = parseInt(size[1], 10);
      }

      if (size[0] === 'auto') {
          width = height / image.height * image.width;
      }

      return {width: width, height: height};
  };

  NodeContainer.prototype.parseBackgroundPosition = function(bounds, image, index, backgroundSize) {
      var position = this.cssList('backgroundPosition', index);
      var left, top;

      if (isPercentage(position[0])){
          left = (bounds.width - (backgroundSize || image).width) * (parseFloat(position[0]) / 100);
      } else {
          left = parseInt(position[0], 10);
      }

      if (position[1] === 'auto') {
          top = left / image.width * image.height;
      } else if (isPercentage(position[1])){
          top =  (bounds.height - (backgroundSize || image).height) * parseFloat(position[1]) / 100;
      } else {
          top = parseInt(position[1], 10);
      }

      if (position[0] === 'auto') {
          left = top / image.height * image.width;
      }

      return {left: left, top: top};
  };

  NodeContainer.prototype.parseBackgroundRepeat = function(index) {
      return this.cssList("backgroundRepeat", index)[0];
  };

  NodeContainer.prototype.parseTextShadows = function() {
      var textShadow = this.css("textShadow");
      var results = [];

      if (textShadow && textShadow !== 'none') {
          var shadows = textShadow.match(this.TEXT_SHADOW_PROPERTY);
          for (var i = 0; shadows && (i < shadows.length); i++) {
              var s = shadows[i].match(this.TEXT_SHADOW_VALUES);
              results.push({
                  color: s[0],
                  offsetX: s[1] ? parseFloat(s[1].replace('px', '')) : 0,
                  offsetY: s[2] ? parseFloat(s[2].replace('px', '')) : 0,
                  blur: s[3] ? s[3].replace('px', '') : 0
              });
          }
      }
      return results;
  };

  NodeContainer.prototype.parseTransform = function() {
      if (!this.transformData) {
          if (this.hasTransform()) {
              var offset = this.parseBounds();
              var origin = this.prefixedCss("transformOrigin").split(" ").map(removePx).map(asFloat);
              origin[0] += offset.left;
              origin[1] += offset.top;
              this.transformData = {
                  origin: origin,
                  matrix: this.parseTransformMatrix()
              };
          } else {
              this.transformData = {
                  origin: [0, 0],
                  matrix: [1, 0, 0, 1, 0, 0]
              };
          }
      }
      return this.transformData;
  };

  NodeContainer.prototype.parseTransformMatrix = function() {
      if (!this.transformMatrix) {
          var transform = this.prefixedCss("transform");
          var matrix = transform ? parseMatrix(transform.match(this.MATRIX_PROPERTY)) : null;
          this.transformMatrix = matrix ? matrix : [1, 0, 0, 1, 0, 0];
      }
      return this.transformMatrix;
  };

  NodeContainer.prototype.parseBounds = function() {
      return this.bounds || (this.bounds = this.hasTransform() ? offsetBounds(this.node) : getBounds(this.node));
  };

  NodeContainer.prototype.hasTransform = function() {
      return this.parseTransformMatrix().join(",") !== "1,0,0,1,0,0" || (this.parent && this.parent.hasTransform());
  };

  NodeContainer.prototype.getValue = function() {
      var value = this.node.value || "";
      value = (this.node.tagName === "SELECT") ? selectionValue(this.node) : value;
      return value.length === 0 ? (this.node.placeholder || "") : value;
  };

  NodeContainer.prototype.MATRIX_PROPERTY = /(matrix)\((.+)\)/;
  NodeContainer.prototype.TEXT_SHADOW_PROPERTY = /((rgba|rgb)\([^\)]+\)(\s-?\d+px){0,})/g;
  NodeContainer.prototype.TEXT_SHADOW_VALUES = /(-?\d+px)|(#.+)|(rgb\(.+\))|(rgba\(.+\))/g;
  NodeContainer.prototype.CLIP = /^rect\((\d+)px,? (\d+)px,? (\d+)px,? (\d+)px\)$/;

  function selectionValue(node) {
      var option = node.options[node.selectedIndex || 0];
      return option ? (option.text || "") : "";
  }

  function parseMatrix(match) {
      if (match && match[1] === "matrix") {
          return match[2].split(",").map(function(s) {
              return parseFloat(s.trim());
          });
      }
  }

  function isPercentage(value) {
      return value.toString().indexOf("%") !== -1;
  }

  function parseBackgrounds(backgroundImage) {
      var whitespace = ' \r\n\t',
          method, definition, prefix, prefix_i, block, results = [],
          mode = 0, numParen = 0, quote, args;
      var appendResult = function() {
          if(method) {
              if (definition.substr(0, 1) === '"') {
                  definition = definition.substr(1, definition.length - 2);
              }
              if (definition) {
                  args.push(definition);
              }
              if (method.substr(0, 1) === '-' && (prefix_i = method.indexOf('-', 1 ) + 1) > 0) {
                  prefix = method.substr(0, prefix_i);
                  method = method.substr(prefix_i);
              }
              results.push({
                  prefix: prefix,
                  method: method.toLowerCase(),
                  value: block,
                  args: args,
                  image: null
              });
          }
          args = [];
          method = prefix = definition = block = '';
      };
      args = [];
      method = prefix = definition = block = '';
      backgroundImage.split("").forEach(function(c) {
          if (mode === 0 && whitespace.indexOf(c) > -1) {
              return;
          }
          switch(c) {
          case '"':
              if(!quote) {
                  quote = c;
              } else if(quote === c) {
                  quote = null;
              }
              break;
          case '(':
              if(quote) {
                  break;
              } else if(mode === 0) {
                  mode = 1;
                  block += c;
                  return;
              } else {
                  numParen++;
              }
              break;
          case ')':
              if (quote) {
                  break;
              } else if(mode === 1) {
                  if(numParen === 0) {
                      mode = 0;
                      block += c;
                      appendResult();
                      return;
                  } else {
                      numParen--;
                  }
              }
              break;

          case ',':
              if (quote) {
                  break;
              } else if(mode === 0) {
                  appendResult();
                  return;
              } else if (mode === 1) {
                  if (numParen === 0 && !method.match(/^url$/i)) {
                      args.push(definition);
                      definition = '';
                      block += c;
                      return;
                  }
              }
              break;
          }

          block += c;
          if (mode === 0) {
              method += c;
          } else {
              definition += c;
          }
      });

      appendResult();
      return results;
  }

  function removePx(str) {
      return str.replace("px", "");
  }

  function asFloat(str) {
      return parseFloat(str);
  }

  function getBounds(node) {
      if (node.getBoundingClientRect) {
          var clientRect = node.getBoundingClientRect();
          var width = node.offsetWidth == null ? clientRect.width : node.offsetWidth;
          return {
              top: clientRect.top,
              bottom: clientRect.bottom || (clientRect.top + clientRect.height),
              right: clientRect.left + width,
              left: clientRect.left,
              width:  width,
              height: node.offsetHeight == null ? clientRect.height : node.offsetHeight
          };
      }
      return {};
  }

  function offsetBounds(node) {
      var parent = node.offsetParent ? offsetBounds(node.offsetParent) : {top: 0, left: 0};

      return {
          top: node.offsetTop + parent.top,
          bottom: node.offsetTop + node.offsetHeight + parent.top,
          right: node.offsetLeft + parent.left + node.offsetWidth,
          left: node.offsetLeft + parent.left,
          width: node.offsetWidth,
          height: node.offsetHeight
      };
  }

  function NodeParser(element, renderer, support, imageLoader, options) {
      log("Starting NodeParser");
      this.renderer = renderer;
      this.options = options;
      this.range = null;
      this.support = support;
      this.renderQueue = [];
      this.stack = new StackingContext(true, 1, element.ownerDocument, null);
      var parent = new NodeContainer(element, null);
      if (element === element.ownerDocument.documentElement) {
          // http://www.w3.org/TR/css3-background/#special-backgrounds
          var canvasBackground = new NodeContainer(this.renderer.isTransparent(parent.css('backgroundColor')) ? element.ownerDocument.body : element.ownerDocument.documentElement, null);
          renderer.rectangle(0, 0, renderer.width, renderer.height, canvasBackground.css('backgroundColor'));
      }
      parent.visibile = parent.isElementVisible();
      this.createPseudoHideStyles(element.ownerDocument);
      this.disableAnimations(element.ownerDocument);
      this.nodes = flatten([parent].concat(this.getChildren(parent)).filter(function(container) {
          return container.visible = container.isElementVisible();
      }).map(this.getPseudoElements, this));
      this.fontMetrics = new FontMetrics();
      log("Fetched nodes, total:", this.nodes.length);
      log("Calculate overflow clips");
      this.calculateOverflowClips();
      log("Start fetching images");
      this.images = imageLoader.fetch(this.nodes.filter(isElement));
      this.ready = this.images.ready.then(bind(function() {
          log("Images loaded, starting parsing");
          log("Creating stacking contexts");
          this.createStackingContexts();
          log("Sorting stacking contexts");
          this.sortStackingContexts(this.stack);
          this.parse(this.stack);
          log("Render queue created with " + this.renderQueue.length + " items");
          return new Promise(bind(function(resolve) {
              if (!options.async) {
                  this.renderQueue.forEach(this.paint, this);
                  resolve();
              } else if (typeof(options.async) === "function") {
                  options.async.call(this, this.renderQueue, resolve);
              } else if (this.renderQueue.length > 0){
                  this.renderIndex = 0;
                  this.asyncRenderer(this.renderQueue, resolve);
              } else {
                  resolve();
              }
          }, this));
      }, this));
  }

  NodeParser.prototype.calculateOverflowClips = function() {
      this.nodes.forEach(function(container) {
          if (isElement(container)) {
              if (isPseudoElement(container)) {
                  container.appendToDOM();
              }
              container.borders = this.parseBorders(container);
              var clip = (container.css('overflow') === "hidden") ? [container.borders.clip] : [];
              var cssClip = container.parseClip();
              if (cssClip && ["absolute", "fixed"].indexOf(container.css('position')) !== -1) {
                  clip.push([["rect",
                          container.bounds.left + cssClip.left,
                          container.bounds.top + cssClip.top,
                          cssClip.right - cssClip.left,
                          cssClip.bottom - cssClip.top
                  ]]);
              }
              container.clip = hasParentClip(container) ? container.parent.clip.concat(clip) : clip;
              container.backgroundClip = (container.css('overflow') !== "hidden") ? container.clip.concat([container.borders.clip]) : container.clip;
              if (isPseudoElement(container)) {
                  container.cleanDOM();
              }
          } else if (isTextNode(container)) {
              container.clip = hasParentClip(container) ? container.parent.clip : [];
          }
          if (!isPseudoElement(container)) {
              container.bounds = null;
          }
      }, this);
  };

  function hasParentClip(container) {
      return container.parent && container.parent.clip.length;
  }

  NodeParser.prototype.asyncRenderer = function(queue, resolve, asyncTimer) {
      asyncTimer = asyncTimer || Date.now();
      this.paint(queue[this.renderIndex++]);
      if (queue.length === this.renderIndex) {
          resolve();
      } else if (asyncTimer + 20 > Date.now()) {
          this.asyncRenderer(queue, resolve, asyncTimer);
      } else {
          setTimeout(bind(function() {
              this.asyncRenderer(queue, resolve);
          }, this), 0);
      }
  };

  NodeParser.prototype.createPseudoHideStyles = function(document) {
      this.createStyles(document, '.' + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + ':before { content: "" !important; display: none !important; }' +
          '.' + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER + ':after { content: "" !important; display: none !important; }');
  };

  NodeParser.prototype.disableAnimations = function(document) {
      this.createStyles(document, '* { -webkit-animation: none !important; -moz-animation: none !important; -o-animation: none !important; animation: none !important; ' +
          '-webkit-transition: none !important; -moz-transition: none !important; -o-transition: none !important; transition: none !important;}');
  };

  NodeParser.prototype.createStyles = function(document, styles) {
      var hidePseudoElements = document.createElement('style');
      hidePseudoElements.innerHTML = styles;
      document.body.appendChild(hidePseudoElements);
  };

  NodeParser.prototype.getPseudoElements = function(container) {
      var nodes = [[container]];
      if (container.node.nodeType === Node.ELEMENT_NODE) {
          var before = this.getPseudoElement(container, ":before");
          var after = this.getPseudoElement(container, ":after");

          if (before) {
              nodes.push(before);
          }

          if (after) {
              nodes.push(after);
          }
      }
      return flatten(nodes);
  };

  function toCamelCase(str) {
      return str.replace(/(\-[a-z])/g, function(match){
          return match.toUpperCase().replace('-','');
      });
  }

  NodeParser.prototype.getPseudoElement = function(container, type) {
      var style = container.computedStyle(type);
      if(!style || !style.content || style.content === "none" || style.content === "-moz-alt-content" || style.display === "none") {
          return null;
      }

      var content = stripQuotes(style.content);
      var isImage = content.substr(0, 3) === 'url';
      var pseudoNode = document.createElement(isImage ? 'img' : 'html2canvaspseudoelement');
      var pseudoContainer = new PseudoElementContainer(pseudoNode, container, type);

      for (var i = style.length-1; i >= 0; i--) {
          var property = toCamelCase(style.item(i));
          pseudoNode.style[property] = style[property];
      }

      pseudoNode.className = PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE + " " + PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER;

      if (isImage) {
          pseudoNode.src = parseBackgrounds(content)[0].args[0];
          return [pseudoContainer];
      } else {
          var text = document.createTextNode(content);
          pseudoNode.appendChild(text);
          return [pseudoContainer, new TextContainer(text, pseudoContainer)];
      }
  };


  NodeParser.prototype.getChildren = function(parentContainer) {
      return flatten([].filter.call(parentContainer.node.childNodes, renderableNode).map(function(node) {
          var container = [node.nodeType === Node.TEXT_NODE ? new TextContainer(node, parentContainer) : new NodeContainer(node, parentContainer)].filter(nonIgnoredElement);
          return node.nodeType === Node.ELEMENT_NODE && container.length && node.tagName !== "TEXTAREA" ? (container[0].isElementVisible() ? container.concat(this.getChildren(container[0])) : []) : container;
      }, this));
  };

  NodeParser.prototype.newStackingContext = function(container, hasOwnStacking) {
      var stack = new StackingContext(hasOwnStacking, container.getOpacity(), container.node, container.parent);
      container.cloneTo(stack);
      var parentStack = hasOwnStacking ? stack.getParentStack(this) : stack.parent.stack;
      parentStack.contexts.push(stack);
      container.stack = stack;
  };

  NodeParser.prototype.createStackingContexts = function() {
      this.nodes.forEach(function(container) {
          if (isElement(container) && (this.isRootElement(container) || hasOpacity(container) || isPositionedForStacking(container) || this.isBodyWithTransparentRoot(container) || container.hasTransform())) {
              this.newStackingContext(container, true);
          } else if (isElement(container) && ((isPositioned(container) && zIndex0(container)) || isInlineBlock(container) || isFloating(container))) {
              this.newStackingContext(container, false);
          } else {
              container.assignStack(container.parent.stack);
          }
      }, this);
  };

  NodeParser.prototype.isBodyWithTransparentRoot = function(container) {
      return container.node.nodeName === "BODY" && this.renderer.isTransparent(container.parent.css('backgroundColor'));
  };

  NodeParser.prototype.isRootElement = function(container) {
      return container.parent === null;
  };

  NodeParser.prototype.sortStackingContexts = function(stack) {
      stack.contexts.sort(zIndexSort(stack.contexts.slice(0)));
      stack.contexts.forEach(this.sortStackingContexts, this);
  };

  NodeParser.prototype.parseTextBounds = function(container) {
      return function(text, index, textList) {
          if (container.parent.css("textDecoration").substr(0, 4) !== "none" || text.trim().length !== 0) {
              if (this.support.rangeBounds && !container.parent.hasTransform()) {
                  var offset = textList.slice(0, index).join("").length;
                  return this.getRangeBounds(container.node, offset, text.length);
              } else if (container.node && typeof(container.node.data) === "string") {
                  var replacementNode = container.node.splitText(text.length);
                  var bounds = this.getWrapperBounds(container.node, container.parent.hasTransform());
                  container.node = replacementNode;
                  return bounds;
              }
          } else if(!this.support.rangeBounds || container.parent.hasTransform()){
              container.node = container.node.splitText(text.length);
          }
          return {};
      };
  };

  NodeParser.prototype.getWrapperBounds = function(node, transform) {
      var wrapper = node.ownerDocument.createElement('html2canvaswrapper');
      var parent = node.parentNode,
          backupText = node.cloneNode(true);

      wrapper.appendChild(node.cloneNode(true));
      parent.replaceChild(wrapper, node);
      var bounds = transform ? offsetBounds(wrapper) : getBounds(wrapper);
      parent.replaceChild(backupText, wrapper);
      return bounds;
  };

  NodeParser.prototype.getRangeBounds = function(node, offset, length) {
      var range = this.range || (this.range = node.ownerDocument.createRange());
      range.setStart(node, offset);
      range.setEnd(node, offset + length);
      return range.getBoundingClientRect();
  };

  function ClearTransform() {}

  NodeParser.prototype.parse = function(stack) {
      // http://www.w3.org/TR/CSS21/visuren.html#z-index
      var negativeZindex = stack.contexts.filter(negativeZIndex); // 2. the child stacking contexts with negative stack levels (most negative first).
      var descendantElements = stack.children.filter(isElement);
      var descendantNonFloats = descendantElements.filter(not(isFloating));
      var nonInlineNonPositionedDescendants = descendantNonFloats.filter(not(isPositioned)).filter(not(inlineLevel)); // 3 the in-flow, non-inline-level, non-positioned descendants.
      var nonPositionedFloats = descendantElements.filter(not(isPositioned)).filter(isFloating); // 4. the non-positioned floats.
      var inFlow = descendantNonFloats.filter(not(isPositioned)).filter(inlineLevel); // 5. the in-flow, inline-level, non-positioned descendants, including inline tables and inline blocks.
      var stackLevel0 = stack.contexts.concat(descendantNonFloats.filter(isPositioned)).filter(zIndex0); // 6. the child stacking contexts with stack level 0 and the positioned descendants with stack level 0.
      var text = stack.children.filter(isTextNode).filter(hasText);
      var positiveZindex = stack.contexts.filter(positiveZIndex); // 7. the child stacking contexts with positive stack levels (least positive first).
      negativeZindex.concat(nonInlineNonPositionedDescendants).concat(nonPositionedFloats)
          .concat(inFlow).concat(stackLevel0).concat(text).concat(positiveZindex).forEach(function(container) {
              this.renderQueue.push(container);
              if (isStackingContext(container)) {
                  this.parse(container);
                  this.renderQueue.push(new ClearTransform());
              }
          }, this);
  };

  NodeParser.prototype.paint = function(container) {
      try {
          if (container instanceof ClearTransform) {
              this.renderer.ctx.restore();
          } else if (isTextNode(container)) {
              if (isPseudoElement(container.parent)) {
                  container.parent.appendToDOM();
              }
              this.paintText(container);
              if (isPseudoElement(container.parent)) {
                  container.parent.cleanDOM();
              }
          } else {
              this.paintNode(container);
          }
      } catch(e) {
          log(e);
      }
  };

  NodeParser.prototype.paintNode = function(container) {
      if (isStackingContext(container)) {
          this.renderer.setOpacity(container.opacity);
          this.renderer.ctx.save();
          if (container.hasTransform()) {
              this.renderer.setTransform(container.parseTransform());
          }
      }
      if (container.node.nodeName === "INPUT" && container.node.type === "checkbox") {
          this.paintCheckbox(container);
      } else if (container.node.nodeName === "INPUT" && container.node.type === "radio") {
          this.paintRadio(container);
      } else {
          if (container.css('page-break-before') === 'always'){
          	var c = this.options.canvas.getContext('2d');
          	if (typeof c._pageBreakAt === 'function' ){
          		c._pageBreakAt(container.node.offsetTop);
          	}
          }
          this.paintElement(container);
      }
      if (container.node.getAttribute){
  		   var name = container.node.getAttribute('name');
  		   if (name === null){
  			   var name = container.node.getAttribute('id')			   
  		   }
  		   if (name !== null){
  			   var annotations = this.options.canvas.annotations;
  			   if (annotations){
  				   annotations.setName(name, container.bounds);
  			   }
  		   }
  	}
  };

  NodeParser.prototype.paintElement = function(container) {
      var bounds = container.parseBounds();
      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.renderBackground(container, bounds, container.borders.borders.map(getWidth));
      }, this);

      this.renderer.clip(container.clip, function() {
          this.renderer.renderBorders(container.borders.borders);
      }, this);

      this.renderer.clip(container.backgroundClip, function() {
          switch (container.node.nodeName) {
          case "svg":
          case "IFRAME":
              var imgContainer = this.images.get(container.node);
              if (imgContainer) {
                  this.renderer.renderImage(container, bounds, container.borders, imgContainer);
              } else {
                  log("Error loading <" + container.node.nodeName + ">", container.node);
              }
              break;
          case "IMG":
              var imageContainer = this.images.get(container.node.src);
              if (imageContainer) {
                  this.renderer.renderImage(container, bounds, container.borders, imageContainer);
              } else {
                  log("Error loading <img>", container.node.src);
              }
              break;
          case "CANVAS":
              this.renderer.renderImage(container, bounds, container.borders, {image: container.node});
              break;
          case "SELECT":
          case "INPUT":
          case "TEXTAREA":
              this.paintFormValue(container);
              break;
          }
      }, this);
  };

  NodeParser.prototype.paintCheckbox = function(container) {
      var b = container.parseBounds();

      var size = Math.min(b.width, b.height);
      var bounds = {width: size - 1, height: size - 1, top: b.top, left: b.left};
      var r = [3, 3];
      var radius = [r, r, r, r];
      var borders = [1,1,1,1].map(function(w) {
          return {color: '#A5A5A5', width: w};
      });

      var borderPoints = calculateCurvePoints(bounds, radius, borders);

      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.rectangle(bounds.left + 1, bounds.top + 1, bounds.width - 2, bounds.height - 2, "#DEDEDE");
          this.renderer.renderBorders(calculateBorders(borders, bounds, borderPoints, radius));
          if (container.node.checked) {
              this.renderer.font('#424242', 'normal', 'normal', 'bold', (size - 3) + "px", 'arial');
              this.renderer.text("\u2714", bounds.left + size / 6, bounds.top + size - 1);
          }
      }, this);
  };

  NodeParser.prototype.paintRadio = function(container) {
      var bounds = container.parseBounds();

      var size = Math.min(bounds.width, bounds.height) - 2;

      this.renderer.clip(container.backgroundClip, function() {
          this.renderer.circleStroke(bounds.left + 1, bounds.top + 1, size, '#DEDEDE', 1, '#A5A5A5');
          if (container.node.checked) {
              this.renderer.circle(Math.ceil(bounds.left + size / 4) + 1, Math.ceil(bounds.top + size / 4) + 1, Math.floor(size / 2), '#424242');
          }
      }, this);
  };

  NodeParser.prototype.paintFormValue = function(container) {
      if (container.getValue().length > 0) {
          var document = container.node.ownerDocument;
          var wrapper = document.createElement('html2canvaswrapper');
          var properties = ['lineHeight', 'textAlign', 'fontFamily', 'fontWeight', 'fontSize', 'color',
              'paddingLeft', 'paddingTop', 'paddingRight', 'paddingBottom',
              'width', 'height', 'borderLeftStyle', 'borderTopStyle', 'borderLeftWidth', 'borderTopWidth',
              'boxSizing', 'whiteSpace', 'wordWrap'];

          properties.forEach(function(property) {
              try {
                  wrapper.style[property] = container.css(property);
              } catch(e) {
                  // Older IE has issues with "border"
                  log("html2canvas: Parse: Exception caught in renderFormValue: " + e.message);
              }
          });
          var bounds = container.parseBounds();
          wrapper.style.position = "fixed";
          wrapper.style.left = bounds.left + "px";
          wrapper.style.top = bounds.top + "px";
          wrapper.textContent = container.getValue();
          document.body.appendChild(wrapper);
          this.paintText(new TextContainer(wrapper.firstChild, container));
          document.body.removeChild(wrapper);
      }
  };

  NodeParser.prototype.paintText = function(container) {
      container.applyTextTransform();
      var characters = window.html2canvas.punycode.ucs2.decode(container.node.data);
      var textList = (!this.options.letterRendering || noLetterSpacing(container)) && !hasUnicode(container.node.data) ? getWords(characters) : characters.map(function(character) {
          return window.html2canvas.punycode.ucs2.encode([character]);
      });

      var weight = container.parent.fontWeight();
      var size = container.parent.css('fontSize');
      var family = container.parent.css('fontFamily');
      var shadows = container.parent.parseTextShadows();

      this.renderer.font(container.parent.css('color'), container.parent.css('fontStyle'), container.parent.css('fontVariant'), weight, size, family);
      if (shadows.length) {
          // TODO: support multiple text shadows
          this.renderer.fontShadow(shadows[0].color, shadows[0].offsetX, shadows[0].offsetY, shadows[0].blur);
      } else {
          this.renderer.clearShadow();
      }

      this.renderer.clip(container.parent.clip, function() {
          textList.map(this.parseTextBounds(container), this).forEach(function(bounds, index) {
              if (bounds) {
              	if (bounds.left === undefined){
              		bounds.left = 0;
              	}
              	if (bounds.bottom === undefined){
              		bounds.bottom = 0;
              	}
                  this.renderer.text(textList[index], bounds.left, bounds.bottom);
                  this.renderTextDecoration(container.parent, bounds, this.fontMetrics.getMetrics(family, size));
                  if (index == 0 && container.parent.node.nodeName === 'LI'){
                  	this.renderBullet(container, bounds);
                  }
                  if (index == 0){
                  	this.renderAnnotation(container.parent, bounds)                	
                  }
              }
          }, this);
      }, this);
  };

  NodeParser.prototype.generateListNumber = {
  	listAlpha : function(number) {
  		var tmp = "", modulus;
  	
  		do {
  			modulus = number % 26;
  			tmp = String.fromCharCode((modulus) + 64) + tmp;
  			number = number / 26;
  		} while ((number * 26) > 26);
  	
  		return tmp;
  	},
  	
  	listRoman : function(number) {
  		var romanArray = [
  				"M", "CM", "D", "CD", "C", "XC", "L", "XL", "X", "IX", "V", "IV", "I"
  		], decimal = [
  				1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1
  		], roman = "", v, len = romanArray.length;
  	
  		if (number <= 0 || number >= 4000) {
  			return number;
  		}
  	
  		for (v = 0; v < len; v += 1) {
  			while (number >= decimal[v]) {
  				number -= decimal[v];
  				roman += romanArray[v];
  			}
  		}
  	
  		return roman;
  	}
  };


  NodeParser.prototype.listItemText = function (type, currentIndex) {
  	switch (type) {
  	case "decimal-leading-zero":
  		text = (currentIndex.toString().length === 1) ? currentIndex = "0" + currentIndex.toString() : currentIndex.toString();
  		break;
  	case "upper-roman":
  		text = this.generateListNumber.listRoman(currentIndex);
  		break;
  	case "lower-roman":
  		text = this.generateListNumber.listRoman(currentIndex).toLowerCase();
  		break;
  	case "lower-alpha":
  		text = this.generateListNumber.listAlpha(currentIndex).toLowerCase();
  		break;
  	case "upper-alpha":
  		text = this.generateListNumber.listAlpha(currentIndex);
  		break;
  	case "decimal":
  	default:
  		text = currentIndex;
  			break;
  	}

  	return text;
  }

  NodeParser.prototype.renderBullet = function(container, bounds){
  	var type = container.parent.css('listStyleType');
  	if (type === 'none'){
  		return;
  	}
  	var y = bounds.top + (bounds.bottom - bounds.top) / 2;
  	var c2d = this.renderer.canvas.getContext("2d");
  	var textWidth= c2d.measureText("M").width;
  	var size = textWidth/4;
  	var padding = textWidth * .75;
  	var x = bounds.left - padding;
  	switch(type){
  		case 'decimal':
  		case "decimal-leading-zero":
  		case 'upper-alpha':
  		case 'lower-alpha':
  		case 'upper-roman':
  		case 'lower-roman':
  			var li = container.parent;
  			var list = li.parent;
  			var nodeList = Array.prototype.slice.call(list.node.children);
  			var index = nodeList.indexOf(li.node) + 1;
  			
  			var value = this.listItemText(type, index);
  			value += '.';
  			var left = bounds.left - padding;
  			left -= c2d.measureText(value).width;
  			c2d.fillText(value, left, bounds.bottom);
  			break;
  		case 'square':
  			var size = textWidth/3;
  			x -= size;
  			y -= size / 2;
  			c2d.fillRect(x, y , size, size);
  			break;
  		case 'circle':
  			var size = textWidth/6;
  			x -= size;
  			c2d.beginPath();
  			c2d.arc(x, y, size, 0, Math.PI * 2);
  			c2d.closePath();
  			c2d.stroke();
  			break;
  		case 'disc':
  		default:
  			var size = textWidth/6;
  			x -= size;
  			c2d.beginPath();
  			c2d.arc(x, y, size, 0, Math.PI * 2);
  			c2d.closePath();
  			c2d.fill();
  			break;
  	}
  }

  NodeParser.prototype.renderTextDecoration = function(container, bounds, metrics) {
      switch(container.css("textDecoration").split(" ")[0]) {
      case "underline":
          // Draws a line at the baseline of the font
          // TODO As some browsers display the line as more than 1px if the font-size is big, need to take that into account both in position and size
          this.renderer.rectangle(bounds.left, Math.round(bounds.top + metrics.baseline + metrics.lineWidth), bounds.width, 1, container.css("color"));
          break;
      case "overline":
          this.renderer.rectangle(bounds.left, Math.round(bounds.top), bounds.width, 1, container.css("color"));
          break;
      case "line-through":
          // TODO try and find exact position for line-through
          this.renderer.rectangle(bounds.left, Math.ceil(bounds.top + metrics.middle + metrics.lineWidth), bounds.width, 1, container.css("color"));
          break;
      }
  };

  /**
   * Used for hyperlinks and PDF annotations
   * @param container
   * @param bounds
   */
  NodeParser.prototype.renderAnnotation = function(container, bounds) {
     if (container.node.nodeName === 'A'){
  	   var href = container.node.getAttribute('href');
  	   if (href){		   
  		   var annotations = this.options.canvas.annotations;
  		   if (annotations){
  			   annotations.createAnnotation(href, container.bounds);
  		   }
  	   }
     }
  };

  NodeParser.prototype.parseBorders = function(container) {
      var nodeBounds = container.parseBounds();
      var radius = getBorderRadiusData(container);
      var borders = ["Top", "Right", "Bottom", "Left"].map(function(side) {
          return {
              width: container.cssInt('border' + side + 'Width'),
              color: container.css('border' + side + 'Color'),
              args: null
          };
      });
      var borderPoints = calculateCurvePoints(nodeBounds, radius, borders);

      return {
          clip: this.parseBackgroundClip(container, borderPoints, borders, radius, nodeBounds),
          borders: calculateBorders(borders, nodeBounds, borderPoints, radius)
      };
  };

  function calculateBorders(borders, nodeBounds, borderPoints, radius) {
      return borders.map(function(border, borderSide) {
          if (border.width > 0) {
              var bx = nodeBounds.left;
              var by = nodeBounds.top;
              var bw = nodeBounds.width;
              var bh = nodeBounds.height - (borders[2].width);

              switch(borderSide) {
              case 0:
                  // top border
                  bh = borders[0].width;
                  border.args = drawSide({
                          c1: [bx, by],
                          c2: [bx + bw, by],
                          c3: [bx + bw - borders[1].width, by + bh],
                          c4: [bx + borders[3].width, by + bh]
                      }, radius[0], radius[1],
                      borderPoints.topLeftOuter, borderPoints.topLeftInner, borderPoints.topRightOuter, borderPoints.topRightInner);
                  break;
              case 1:
                  // right border
                  bx = nodeBounds.left + nodeBounds.width - (borders[1].width);
                  bw = borders[1].width;

                  border.args = drawSide({
                          c1: [bx + bw, by],
                          c2: [bx + bw, by + bh + borders[2].width],
                          c3: [bx, by + bh],
                          c4: [bx, by + borders[0].width]
                      }, radius[1], radius[2],
                      borderPoints.topRightOuter, borderPoints.topRightInner, borderPoints.bottomRightOuter, borderPoints.bottomRightInner);
                  break;
              case 2:
                  // bottom border
                  by = (by + nodeBounds.height) - (borders[2].width);
                  bh = borders[2].width;
                  border.args = drawSide({
                          c1: [bx + bw, by + bh],
                          c2: [bx, by + bh],
                          c3: [bx + borders[3].width, by],
                          c4: [bx + bw - borders[3].width, by]
                      }, radius[2], radius[3],
                      borderPoints.bottomRightOuter, borderPoints.bottomRightInner, borderPoints.bottomLeftOuter, borderPoints.bottomLeftInner);
                  break;
              case 3:
                  // left border
                  bw = borders[3].width;
                  border.args = drawSide({
                          c1: [bx, by + bh + borders[2].width],
                          c2: [bx, by],
                          c3: [bx + bw, by + borders[0].width],
                          c4: [bx + bw, by + bh]
                      }, radius[3], radius[0],
                      borderPoints.bottomLeftOuter, borderPoints.bottomLeftInner, borderPoints.topLeftOuter, borderPoints.topLeftInner);
                  break;
              }
          }
          return border;
      });
  }

  NodeParser.prototype.parseBackgroundClip = function(container, borderPoints, borders, radius, bounds) {
      var backgroundClip = container.css('backgroundClip'),
          borderArgs = [];

      switch(backgroundClip) {
      case "content-box":
      case "padding-box":
          parseCorner(borderArgs, radius[0], radius[1], borderPoints.topLeftInner, borderPoints.topRightInner, bounds.left + borders[3].width, bounds.top + borders[0].width);
          parseCorner(borderArgs, radius[1], radius[2], borderPoints.topRightInner, borderPoints.bottomRightInner, bounds.left + bounds.width - borders[1].width, bounds.top + borders[0].width);
          parseCorner(borderArgs, radius[2], radius[3], borderPoints.bottomRightInner, borderPoints.bottomLeftInner, bounds.left + bounds.width - borders[1].width, bounds.top + bounds.height - borders[2].width);
          parseCorner(borderArgs, radius[3], radius[0], borderPoints.bottomLeftInner, borderPoints.topLeftInner, bounds.left + borders[3].width, bounds.top + bounds.height - borders[2].width);
          break;

      default:
          parseCorner(borderArgs, radius[0], radius[1], borderPoints.topLeftOuter, borderPoints.topRightOuter, bounds.left, bounds.top);
          parseCorner(borderArgs, radius[1], radius[2], borderPoints.topRightOuter, borderPoints.bottomRightOuter, bounds.left + bounds.width, bounds.top);
          parseCorner(borderArgs, radius[2], radius[3], borderPoints.bottomRightOuter, borderPoints.bottomLeftOuter, bounds.left + bounds.width, bounds.top + bounds.height);
          parseCorner(borderArgs, radius[3], radius[0], borderPoints.bottomLeftOuter, borderPoints.topLeftOuter, bounds.left, bounds.top + bounds.height);
          break;
      }

      return borderArgs;
  };

  function getCurvePoints(x, y, r1, r2) {
      var kappa = 4 * ((Math.sqrt(2) - 1) / 3);
      var ox = (r1) * kappa, // control point offset horizontal
          oy = (r2) * kappa, // control point offset vertical
          xm = x + r1, // x-middle
          ym = y + r2; // y-middle
      return {
          topLeft: bezierCurve({x: x, y: ym}, {x: x, y: ym - oy}, {x: xm - ox, y: y}, {x: xm, y: y}),
          topRight: bezierCurve({x: x, y: y}, {x: x + ox,y: y}, {x: xm, y: ym - oy}, {x: xm, y: ym}),
          bottomRight: bezierCurve({x: xm, y: y}, {x: xm, y: y + oy}, {x: x + ox, y: ym}, {x: x, y: ym}),
          bottomLeft: bezierCurve({x: xm, y: ym}, {x: xm - ox, y: ym}, {x: x, y: y + oy}, {x: x, y:y})
      };
  }

  function calculateCurvePoints(bounds, borderRadius, borders) {
      var x = bounds.left,
          y = bounds.top,
          width = bounds.width,
          height = bounds.height,

          tlh = borderRadius[0][0],
          tlv = borderRadius[0][1],
          trh = borderRadius[1][0],
          trv = borderRadius[1][1],
          brh = borderRadius[2][0],
          brv = borderRadius[2][1],
          blh = borderRadius[3][0],
          blv = borderRadius[3][1];

      var topWidth = width - trh,
          rightHeight = height - brv,
          bottomWidth = width - brh,
          leftHeight = height - blv;

      return {
          topLeftOuter: getCurvePoints(x, y, tlh, tlv).topLeft.subdivide(0.5),
          topLeftInner: getCurvePoints(x + borders[3].width, y + borders[0].width, Math.max(0, tlh - borders[3].width), Math.max(0, tlv - borders[0].width)).topLeft.subdivide(0.5),
          topRightOuter: getCurvePoints(x + topWidth, y, trh, trv).topRight.subdivide(0.5),
          topRightInner: getCurvePoints(x + Math.min(topWidth, width + borders[3].width), y + borders[0].width, (topWidth > width + borders[3].width) ? 0 :trh - borders[3].width, trv - borders[0].width).topRight.subdivide(0.5),
          bottomRightOuter: getCurvePoints(x + bottomWidth, y + rightHeight, brh, brv).bottomRight.subdivide(0.5),
          bottomRightInner: getCurvePoints(x + Math.min(bottomWidth, width - borders[3].width), y + Math.min(rightHeight, height + borders[0].width), Math.max(0, brh - borders[1].width),  brv - borders[2].width).bottomRight.subdivide(0.5),
          bottomLeftOuter: getCurvePoints(x, y + leftHeight, blh, blv).bottomLeft.subdivide(0.5),
          bottomLeftInner: getCurvePoints(x + borders[3].width, y + leftHeight, Math.max(0, blh - borders[3].width), blv - borders[2].width).bottomLeft.subdivide(0.5)
      };
  }

  function bezierCurve(start, startControl, endControl, end) {
      var lerp = function (a, b, t) {
          return {
              x: a.x + (b.x - a.x) * t,
              y: a.y + (b.y - a.y) * t
          };
      };

      return {
          start: start,
          startControl: startControl,
          endControl: endControl,
          end: end,
          subdivide: function(t) {
              var ab = lerp(start, startControl, t),
                  bc = lerp(startControl, endControl, t),
                  cd = lerp(endControl, end, t),
                  abbc = lerp(ab, bc, t),
                  bccd = lerp(bc, cd, t),
                  dest = lerp(abbc, bccd, t);
              return [bezierCurve(start, ab, abbc, dest), bezierCurve(dest, bccd, cd, end)];
          },
          curveTo: function(borderArgs) {
              borderArgs.push(["bezierCurve", startControl.x, startControl.y, endControl.x, endControl.y, end.x, end.y]);
          },
          curveToReversed: function(borderArgs) {
              borderArgs.push(["bezierCurve", endControl.x, endControl.y, startControl.x, startControl.y, start.x, start.y]);
          }
      };
  }

  function drawSide(borderData, radius1, radius2, outer1, inner1, outer2, inner2) {
      var borderArgs = [];

      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", outer1[1].start.x, outer1[1].start.y]);
          outer1[1].curveTo(borderArgs);
      } else {
          borderArgs.push([ "line", borderData.c1[0], borderData.c1[1]]);
      }

      if (radius2[0] > 0 || radius2[1] > 0) {
          borderArgs.push(["line", outer2[0].start.x, outer2[0].start.y]);
          outer2[0].curveTo(borderArgs);
          borderArgs.push(["line", inner2[0].end.x, inner2[0].end.y]);
          inner2[0].curveToReversed(borderArgs);
      } else {
          borderArgs.push(["line", borderData.c2[0], borderData.c2[1]]);
          borderArgs.push(["line", borderData.c3[0], borderData.c3[1]]);
      }

      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", inner1[1].end.x, inner1[1].end.y]);
          inner1[1].curveToReversed(borderArgs);
      } else {
          borderArgs.push(["line", borderData.c4[0], borderData.c4[1]]);
      }

      return borderArgs;
  }

  function parseCorner(borderArgs, radius1, radius2, corner1, corner2, x, y) {
      if (radius1[0] > 0 || radius1[1] > 0) {
          borderArgs.push(["line", corner1[0].start.x, corner1[0].start.y]);
          corner1[0].curveTo(borderArgs);
          corner1[1].curveTo(borderArgs);
      } else {
          borderArgs.push(["line", x, y]);
      }

      if (radius2[0] > 0 || radius2[1] > 0) {
          borderArgs.push(["line", corner2[0].start.x, corner2[0].start.y]);
      }
  }

  function negativeZIndex(container) {
      return container.cssInt("zIndex") < 0;
  }

  function positiveZIndex(container) {
      return container.cssInt("zIndex") > 0;
  }

  function zIndex0(container) {
      return container.cssInt("zIndex") === 0;
  }

  function inlineLevel(container) {
      return ["inline", "inline-block", "inline-table"].indexOf(container.css("display")) !== -1;
  }

  function isStackingContext(container) {
      return (container instanceof StackingContext);
  }

  function hasText(container) {
      return container.node.data.trim().length > 0;
  }

  function noLetterSpacing(container) {
      return (/^(normal|none|0px)$/.test(container.parent.css("letterSpacing")));
  }

  function getBorderRadiusData(container) {
      return ["TopLeft", "TopRight", "BottomRight", "BottomLeft"].map(function(side) {
          var value = container.css('border' + side + 'Radius');
          var arr = value.split(" ");
          if (arr.length <= 1) {
              arr[1] = arr[0];
          }
          return arr.map(asInt);
      });
  }

  function renderableNode(node) {
      return (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE);
  }

  function isPositionedForStacking(container) {
      var position = container.css("position");
      var zIndex = (["absolute", "relative", "fixed"].indexOf(position) !== -1) ? container.css("zIndex") : "auto";
      return zIndex !== "auto";
  }

  function isPositioned(container) {
      return container.css("position") !== "static";
  }

  function isFloating(container) {
      return container.css("float") !== "none";
  }

  function isInlineBlock(container) {
      return ["inline-block", "inline-table"].indexOf(container.css("display")) !== -1;
  }

  function not(callback) {
      var context = this;
      return function() {
          return !callback.apply(context, arguments);
      };
  }

  function isElement(container) {
      return container.node.nodeType === Node.ELEMENT_NODE;
  }

  function isPseudoElement(container) {
      return container.isPseudoElement === true;
  }

  function isTextNode(container) {
      return container.node.nodeType === Node.TEXT_NODE;
  }

  function zIndexSort(contexts) {
      return function(a, b) {
          return (a.cssInt("zIndex") + (contexts.indexOf(a) / contexts.length)) - (b.cssInt("zIndex") + (contexts.indexOf(b) / contexts.length));
      };
  }

  function hasOpacity(container) {
      return container.getOpacity() < 1;
  }

  function bind(callback, context) {
      return function() {
          return callback.apply(context, arguments);
      };
  }

  function asInt(value) {
      return parseInt(value, 10);
  }

  function getWidth(border) {
      return border.width;
  }

  function nonIgnoredElement(nodeContainer) {
      return (nodeContainer.node.nodeType !== Node.ELEMENT_NODE || ["SCRIPT", "HEAD", "TITLE", "OBJECT", "BR", "OPTION"].indexOf(nodeContainer.node.nodeName) === -1);
  }

  function flatten(arrays) {
      return [].concat.apply([], arrays);
  }

  function stripQuotes(content) {
      var first = content.substr(0, 1);
      return (first === content.substr(content.length - 1) && first.match(/'|"/)) ? content.substr(1, content.length - 2) : content;
  }

  function getWords(characters) {
      var words = [], i = 0, onWordBoundary = false, word;
      while(characters.length) {
          if (isWordBoundary(characters[i]) === onWordBoundary) {
              word = characters.splice(0, i);
              if (word.length) {
                  words.push(window.html2canvas.punycode.ucs2.encode(word));
              }
              onWordBoundary =! onWordBoundary;
              i = 0;
          } else {
              i++;
          }

          if (i >= characters.length) {
              word = characters.splice(0, i);
              if (word.length) {
                  words.push(window.html2canvas.punycode.ucs2.encode(word));
              }
          }
      }
      return words;
  }

  function isWordBoundary(characterCode) {
      return [
          32, // <space>
          13, // \r
          10, // \n
          9, // \t
          45 // -
      ].indexOf(characterCode) !== -1;
  }

  function hasUnicode(string) {
      return (/[^\u0000-\u00ff]/).test(string);
  }

  function Proxy(src, proxyUrl, document) {
      var callback = createCallback(supportsCORS);
      var url = createProxyUrl(proxyUrl, src, callback);

      return supportsCORS ? XHR(url) : (jsonp(document, url, callback).then(function(response) {
          return decode64(response.content);
      }));
  }
  var proxyCount = 0;

  var supportsCORS = ('withCredentials' in new XMLHttpRequest());
  var supportsCORSImage = ('crossOrigin' in new Image());

  function ProxyURL(src, proxyUrl, document) {
      var callback = createCallback(supportsCORSImage);
      var url = createProxyUrl(proxyUrl, src, callback);
      return (supportsCORSImage ? Promise.resolve(url) : jsonp(document, url, callback).then(function(response) {
          return "data:" + response.type + ";base64," + response.content;
      }));
  }

  function jsonp(document, url, callback) {
      return new Promise(function(resolve, reject) {
          var s = document.createElement("script");
          var cleanup = function() {
              delete window.html2canvas.proxy[callback];
              document.body.removeChild(s);
          };
          window.html2canvas.proxy[callback] = function(response) {
              cleanup();
              resolve(response);
          };
          s.src = url;
          s.onerror = function(e) {
              cleanup();
              reject(e);
          };
          document.body.appendChild(s);
      });
  }

  function createCallback(useCORS) {
      return !useCORS ? "html2canvas_" + Date.now() + "_" + (++proxyCount) + "_" + Math.round(Math.random() * 100000) : "";
  }

  function createProxyUrl(proxyUrl, src, callback) {
      return proxyUrl + "?url=" + encodeURIComponent(src) + (callback.length ? "&callback=html2canvas.proxy." + callback : "");
  }

  function ProxyImageContainer(src, proxy) {
      var script = document.createElement("script");
      var link = document.createElement("a");
      link.href = src;
      src = link.href;
      this.src = src;
      this.image = new Image();
      var self = this;
      this.promise = new Promise(function(resolve, reject) {
          self.image.crossOrigin = "Anonymous";
          self.image.onload = resolve;
          self.image.onerror = reject;

          new ProxyURL(src, proxy, document).then(function(url) {
              self.image.src = url;
          })['catch'](reject);
      });
  }

  function PseudoElementContainer(node, parent, type) {
      NodeContainer.call(this, node, parent);
      this.isPseudoElement = true;
      this.before = type === ":before";
  }

  PseudoElementContainer.prototype.cloneTo = function(stack) {
      PseudoElementContainer.prototype.cloneTo.call(this, stack);
      stack.isPseudoElement = true;
      stack.before = this.before;
  };

  PseudoElementContainer.prototype = Object.create(NodeContainer.prototype);

  PseudoElementContainer.prototype.appendToDOM = function() {
      if (this.before) {
          this.parent.node.insertBefore(this.node, this.parent.node.firstChild);
      } else {
          this.parent.node.appendChild(this.node);
      }
      this.parent.node.className += " " + this.getHideClass();
  };

  PseudoElementContainer.prototype.cleanDOM = function() {
      this.node.parentNode.removeChild(this.node);
      this.parent.node.className = this.parent.node.className.replace(this.getHideClass(), "");
  };

  PseudoElementContainer.prototype.getHideClass = function() {
      return this["PSEUDO_HIDE_ELEMENT_CLASS_" + (this.before ? "BEFORE" : "AFTER")];
  };

  PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_BEFORE = "___html2canvas___pseudoelement_before";
  PseudoElementContainer.prototype.PSEUDO_HIDE_ELEMENT_CLASS_AFTER = "___html2canvas___pseudoelement_after";

  function Renderer(width, height, images, options, document) {
      this.width = width;
      this.height = height;
      this.images = images;
      this.options = options;
      this.document = document;
  }

  Renderer.prototype.renderImage = function(container, bounds, borderData, imageContainer) {
      var paddingLeft = container.cssInt('paddingLeft'),
          paddingTop = container.cssInt('paddingTop'),
          paddingRight = container.cssInt('paddingRight'),
          paddingBottom = container.cssInt('paddingBottom'),
          borders = borderData.borders;

      var width = bounds.width - (borders[1].width + borders[3].width + paddingLeft + paddingRight);
      var height = bounds.height - (borders[0].width + borders[2].width + paddingTop + paddingBottom);
      this.drawImage(
          imageContainer,
          0,
          0,
          imageContainer.image.width || width,
          imageContainer.image.height || height,
          bounds.left + paddingLeft + borders[3].width,
          bounds.top + paddingTop + borders[0].width,
          width,
          height
      );
  };

  Renderer.prototype.renderBackground = function(container, bounds, borderData) {
      if (bounds.height > 0 && bounds.width > 0) {
          this.renderBackgroundColor(container, bounds);
          this.renderBackgroundImage(container, bounds, borderData);
      }
  };

  Renderer.prototype.renderBackgroundColor = function(container, bounds) {
      var color = container.css("backgroundColor");
      if (!this.isTransparent(color)) {
          this.rectangle(bounds.left, bounds.top, bounds.width, bounds.height, container.css("backgroundColor"));
      }
  };

  Renderer.prototype.renderBorders = function(borders) {
      borders.forEach(this.renderBorder, this);
  };

  Renderer.prototype.renderBorder = function(data) {
      if (!this.isTransparent(data.color) && data.args !== null) {
          this.drawShape(data.args, data.color);
      }
  };

  Renderer.prototype.renderBackgroundImage = function(container, bounds, borderData) {
      var backgroundImages = container.parseBackgroundImages();
      backgroundImages.reverse().forEach(function(backgroundImage, index, arr) {
          switch(backgroundImage.method) {
              case "url":
                  var image = this.images.get(backgroundImage.args[0]);
                  if (image) {
                      this.renderBackgroundRepeating(container, bounds, image, arr.length - (index+1), borderData);
                  } else {
                      log("Error loading background-image", backgroundImage.args[0]);
                  }
                  break;
              case "linear-gradient":
              case "gradient":
                  var gradientImage = this.images.get(backgroundImage.value);
                  if (gradientImage) {
                      this.renderBackgroundGradient(gradientImage, bounds, borderData);
                  } else {
                      log("Error loading background-image", backgroundImage.args[0]);
                  }
                  break;
              case "none":
                  break;
              default:
                  log("Unknown background-image type", backgroundImage.args[0]);
          }
      }, this);
  };

  Renderer.prototype.renderBackgroundRepeating = function(container, bounds, imageContainer, index, borderData) {
      var size = container.parseBackgroundSize(bounds, imageContainer.image, index);
      var position = container.parseBackgroundPosition(bounds, imageContainer.image, index, size);
      var repeat = container.parseBackgroundRepeat(index);
      switch (repeat) {
      case "repeat-x":
      case "repeat no-repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + borderData[3], bounds.top + position.top + borderData[0], 99999, size.height, borderData);
          break;
      case "repeat-y":
      case "no-repeat repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + position.left + borderData[3], bounds.top + borderData[0], size.width, 99999, borderData);
          break;
      case "no-repeat":
          this.backgroundRepeatShape(imageContainer, position, size, bounds, bounds.left + position.left + borderData[3], bounds.top + position.top + borderData[0], size.width, size.height, borderData);
          break;
      default:
          this.renderBackgroundRepeat(imageContainer, position, size, {top: bounds.top, left: bounds.left}, borderData[3], borderData[0]);
          break;
      }
  };

  Renderer.prototype.isTransparent = function(color) {
      return (!color || color === "transparent" || color === "rgba(0, 0, 0, 0)");
  };

  function StackingContext(hasOwnStacking, opacity, element, parent) {
      NodeContainer.call(this, element, parent);
      this.ownStacking = hasOwnStacking;
      this.contexts = [];
      this.children = [];
      this.opacity = (this.parent ? this.parent.stack.opacity : 1) * opacity;
  }

  StackingContext.prototype = Object.create(NodeContainer.prototype);

  StackingContext.prototype.getParentStack = function(context) {
      var parentStack = (this.parent) ? this.parent.stack : null;
      return parentStack ? (parentStack.ownStacking ? parentStack : parentStack.getParentStack(context)) : context.stack;
  };

  function Support(document) {
      this.rangeBounds = this.testRangeBounds(document);
      this.cors = this.testCORS();
      this.svg = this.testSVG();
  }

  Support.prototype.testRangeBounds = function(document) {
      var range, testElement, rangeBounds, rangeHeight, support = false;

      if (document.createRange) {
          range = document.createRange();
          if (range.getBoundingClientRect) {
              testElement = document.createElement('boundtest');
              testElement.style.height = "123px";
              testElement.style.display = "block";
              document.body.appendChild(testElement);

              range.selectNode(testElement);
              rangeBounds = range.getBoundingClientRect();
              rangeHeight = rangeBounds.height;

              if (rangeHeight === 123) {
                  support = true;
              }
              document.body.removeChild(testElement);
          }
      }

      return support;
  };

  Support.prototype.testCORS = function() {
      return typeof((new Image()).crossOrigin) !== "undefined";
  };

  Support.prototype.testSVG = function() {
      var img = new Image();
      var canvas = document.createElement("canvas");
      var ctx =  canvas.getContext("2d");
      img.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>";

      try {
          ctx.drawImage(img, 0, 0);
          canvas.toDataURL();
      } catch(e) {
          return false;
      }
      return true;
  };

  function SVGContainer(src) {
      this.src = src;
      this.image = null;
      var self = this;

      this.promise = this.hasFabric().then(function() {
          return (self.isInline(src) ? Promise.resolve(self.inlineFormatting(src)) : XHR(src));
      }).then(function(svg) {
          return new Promise(function(resolve) {
              html2canvas.fabric.loadSVGFromString(svg, self.createCanvas.call(self, resolve));
          });
      });
  }

  SVGContainer.prototype.hasFabric = function() {
      return !html2canvas.fabric ? Promise.reject(new Error("html2canvas.svg.js is not loaded, cannot render svg")) : Promise.resolve();
  };

  SVGContainer.prototype.inlineFormatting = function(src) {
      return (/^data:image\/svg\+xml;base64,/.test(src)) ? this.decode64(this.removeContentType(src)) : this.removeContentType(src);
  };

  SVGContainer.prototype.removeContentType = function(src) {
      return src.replace(/^data:image\/svg\+xml(;base64)?,/,'');
  };

  SVGContainer.prototype.isInline = function(src) {
      return (/^data:image\/svg\+xml/i.test(src));
  };

  SVGContainer.prototype.createCanvas = function(resolve) {
      var self = this;
      return function (objects, options) {
          var canvas = new html2canvas.fabric.StaticCanvas('c');
          self.image = canvas.lowerCanvasEl;
          canvas
              .setWidth(options.width)
              .setHeight(options.height)
              .add(html2canvas.fabric.util.groupSVGElements(objects, options))
              .renderAll();
          resolve(canvas.lowerCanvasEl);
      };
  };

  SVGContainer.prototype.decode64 = function(str) {
      return (typeof(window.atob) === "function") ? window.atob(str) : decode64(str);
  };

  /*
   * base64-arraybuffer
   * https://github.com/niklasvh/base64-arraybuffer
   *
   * Copyright (c) 2012 Niklas von Hertzen
   * Licensed under the MIT license.
   */

  function decode64(base64) {
      var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
      var len = base64.length, i, encoded1, encoded2, encoded3, encoded4, byte1, byte2, byte3;

      var output = "";

      for (i = 0; i < len; i+=4) {
          encoded1 = chars.indexOf(base64[i]);
          encoded2 = chars.indexOf(base64[i+1]);
          encoded3 = chars.indexOf(base64[i+2]);
          encoded4 = chars.indexOf(base64[i+3]);

          byte1 = (encoded1 << 2) | (encoded2 >> 4);
          byte2 = ((encoded2 & 15) << 4) | (encoded3 >> 2);
          byte3 = ((encoded3 & 3) << 6) | encoded4;
          if (encoded3 === 64) {
              output += String.fromCharCode(byte1);
          } else if (encoded4 === 64 || encoded4 === -1) {
              output += String.fromCharCode(byte1, byte2);
          } else{
              output += String.fromCharCode(byte1, byte2, byte3);
          }
      }

      return output;
  }

  function SVGNodeContainer(node, native) {
      this.src = node;
      this.image = null;
      var self = this;

      this.promise = native ? new Promise(function(resolve, reject) {
          self.image = new Image();
          self.image.onload = resolve;
          self.image.onerror = reject;
          self.image.src = "data:image/svg+xml," + (new XMLSerializer()).serializeToString(node);
          if (self.image.complete === true) {
              resolve(self.image);
          }
      }) : this.hasFabric().then(function() {
          return new Promise(function(resolve) {
              html2canvas.fabric.parseSVGDocument(node, self.createCanvas.call(self, resolve));
          });
      });
  }

  SVGNodeContainer.prototype = Object.create(SVGContainer.prototype);

  function TextContainer(node, parent) {
      NodeContainer.call(this, node, parent);
  }

  TextContainer.prototype = Object.create(NodeContainer.prototype);

  TextContainer.prototype.applyTextTransform = function() {
      this.node.data = this.transform(this.parent.css("textTransform"));
  };

  TextContainer.prototype.transform = function(transform) {
      var text = this.node.data;
      switch(transform){
          case "lowercase":
              return text.toLowerCase();
          case "capitalize":
              return text.replace(/(^|\s|:|-|\(|\))([a-z])/g, capitalize);
          case "uppercase":
              return text.toUpperCase();
          default:
              return text;
      }
  };

  function capitalize(m, p1, p2) {
      if (m.length > 0) {
          return p1 + p2.toUpperCase();
      }
  }

  function WebkitGradientContainer(imageData) {
      GradientContainer.apply(this, arguments);
      this.type = (imageData.args[0] === "linear") ? this.TYPES.LINEAR : this.TYPES.RADIAL;
  }

  WebkitGradientContainer.prototype = Object.create(GradientContainer.prototype);

  function XHR(url) {
      return new Promise(function(resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', url);

          xhr.onload = function() {
              if (xhr.status === 200) {
                  resolve(xhr.responseText);
              } else {
                  reject(new Error(xhr.statusText));
              }
          };

          xhr.onerror = function() {
              reject(new Error("Network Error"));
          };

          xhr.send();
      });
  }

  function CanvasRenderer(width, height) {
      Renderer.apply(this, arguments);
      this.canvas = this.options.canvas || this.document.createElement("canvas");
      if (!this.options.canvas) {
          this.canvas.width = width;
          this.canvas.height = height;
      }
      this.ctx = this.canvas.getContext("2d");
      if (this.options.background) {
          this.rectangle(0, 0, width, height, this.options.background);
      }
      this.taintCtx = this.document.createElement("canvas").getContext("2d");
      this.ctx.textBaseline = "bottom";
      this.variables = {};
      log("Initialized CanvasRenderer with size", width, "x", height);
  }

  CanvasRenderer.prototype = Object.create(Renderer.prototype);

  CanvasRenderer.prototype.setFillStyle = function(color) {
      this.ctx.fillStyle = color;
      return this.ctx;
  };

  CanvasRenderer.prototype.rectangle = function(left, top, width, height, color) {
      this.setFillStyle(color).fillRect(left, top, width, height);
  };

  CanvasRenderer.prototype.circle = function(left, top, size, color) {
      this.setFillStyle(color);
      this.ctx.beginPath();
      this.ctx.arc(left + size / 2, top + size / 2, size / 2, 0, Math.PI*2, true);
      this.ctx.closePath();
      this.ctx.fill();
  };

  CanvasRenderer.prototype.circleStroke = function(left, top, size, color, stroke, strokeColor) {
      this.circle(left, top, size, color);
      this.ctx.strokeStyle = strokeColor;
      this.ctx.stroke();
  };

  CanvasRenderer.prototype.drawShape = function(shape, color) {
      this.shape(shape);
      this.setFillStyle(color).fill();
  };

  CanvasRenderer.prototype.taints = function(imageContainer) {
      if (imageContainer.tainted === null) {
          this.taintCtx.drawImage(imageContainer.image, 0, 0);
          try {
              this.taintCtx.getImageData(0, 0, 1, 1);
              imageContainer.tainted = false;
          } catch(e) {
              this.taintCtx = document.createElement("canvas").getContext("2d");
              imageContainer.tainted = true;
          }
      }

      return imageContainer.tainted;
  };

  CanvasRenderer.prototype.drawImage = function(imageContainer, sx, sy, sw, sh, dx, dy, dw, dh) {
      if (!this.taints(imageContainer) || this.options.allowTaint) {
          this.ctx.drawImage(imageContainer.image, sx, sy, sw, sh, dx, dy, dw, dh);
      }
  };

  CanvasRenderer.prototype.clip = function(shapes, callback, context) {
      this.ctx.save();
      shapes.filter(hasEntries).forEach(function(shape) {
          this.shape(shape).clip();
      }, this);
      callback.call(context);
      this.ctx.restore();
  };

  CanvasRenderer.prototype.shape = function(shape) {
      this.ctx.beginPath();
      shape.forEach(function(point, index) {
          if (point[0] === "rect") {
              this.ctx.rect.apply(this.ctx, point.slice(1));
          } else {
              this.ctx[(index === 0) ? "moveTo" : point[0] + "To" ].apply(this.ctx, point.slice(1));
          }
      }, this);
      this.ctx.closePath();
      return this.ctx;
  };

  CanvasRenderer.prototype.font = function(color, style, variant, weight, size, family) {
      this.setFillStyle(color).font = [style, variant, weight, size, family].join(" ").split(",")[0];
  };

  CanvasRenderer.prototype.fontShadow = function(color, offsetX, offsetY, blur) {
      this.setVariable("shadowColor", color)
          .setVariable("shadowOffsetY", offsetX)
          .setVariable("shadowOffsetX", offsetY)
          .setVariable("shadowBlur", blur);
  };

  CanvasRenderer.prototype.clearShadow = function() {
      this.setVariable("shadowColor", "rgba(0,0,0,0)");
  };

  CanvasRenderer.prototype.setOpacity = function(opacity) {
      this.ctx.globalAlpha = opacity;
  };

  CanvasRenderer.prototype.setTransform = function(transform) {
      this.ctx.translate(transform.origin[0], transform.origin[1]);
      this.ctx.transform.apply(this.ctx, transform.matrix);
      this.ctx.translate(-transform.origin[0], -transform.origin[1]);
  };

  CanvasRenderer.prototype.setVariable = function(property, value) {
      if (this.variables[property] !== value) {
          this.variables[property] = this.ctx[property] = value;
      }

      return this;
  };

  CanvasRenderer.prototype.text = function(text, left, bottom) {
  		this.ctx.fillText(text, left, bottom);		
  };

  CanvasRenderer.prototype.backgroundRepeatShape = function(imageContainer, backgroundPosition, size, bounds, left, top, width, height, borderData) {
      var shape = [
          ["line", Math.round(left), Math.round(top)],
          ["line", Math.round(left + width), Math.round(top)],
          ["line", Math.round(left + width), Math.round(height + top)],
          ["line", Math.round(left), Math.round(height + top)]
      ];
      this.clip([shape], function() {
          this.renderBackgroundRepeat(imageContainer, backgroundPosition, size, bounds, borderData[3], borderData[0]);
      }, this);
  };

  CanvasRenderer.prototype.renderBackgroundRepeat = function(imageContainer, backgroundPosition, size, bounds, borderLeft, borderTop) {
      var offsetX = Math.round(bounds.left + backgroundPosition.left + borderLeft), offsetY = Math.round(bounds.top + backgroundPosition.top + borderTop);
      this.setFillStyle(this.ctx.createPattern(this.resizeImage(imageContainer, size), "repeat"));
      this.ctx.translate(offsetX, offsetY);
      this.ctx.fill();
      this.ctx.translate(-offsetX, -offsetY);
  };

  CanvasRenderer.prototype.renderBackgroundGradient = function(gradientImage, bounds) {
      if (gradientImage instanceof LinearGradientContainer) {
          var gradient = this.ctx.createLinearGradient(
              bounds.left + bounds.width * gradientImage.x0,
              bounds.top + bounds.height * gradientImage.y0,
              bounds.left +  bounds.width * gradientImage.x1,
              bounds.top +  bounds.height * gradientImage.y1);
          gradientImage.colorStops.forEach(function(colorStop) {
              gradient.addColorStop(colorStop.stop, colorStop.color);
          });
          this.rectangle(bounds.left, bounds.top, bounds.width, bounds.height, gradient);
      }
  };

  CanvasRenderer.prototype.resizeImage = function(imageContainer, size) {
      var image = imageContainer.image;
      if(image.width === size.width && image.height === size.height) {
          return image;
      }

      var ctx, canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, size.width, size.height );
      return canvas;
  };

  function hasEntries(array) {
      return array.length > 0;
  }

  }).call({}, window, document);

  // Generated by CoffeeScript 1.4.0

  /*
  # PNG.js
  # Copyright (c) 2011 Devon Govett
  # MIT LICENSE
  # 
  # 
  */


  (function(global) {
    var PNG;

    PNG = (function() {
      var APNG_BLEND_OP_OVER, APNG_BLEND_OP_SOURCE, APNG_DISPOSE_OP_BACKGROUND, APNG_DISPOSE_OP_NONE, APNG_DISPOSE_OP_PREVIOUS, makeImage, scratchCanvas, scratchCtx;

      PNG.load = function(url, canvas, callback) {
        var xhr,
          _this = this;
        if (typeof canvas === 'function') {
          callback = canvas;
        }
        xhr = new XMLHttpRequest;
        xhr.open("GET", url, true);
        xhr.responseType = "arraybuffer";
        xhr.onload = function() {
          var data, png;
          data = new Uint8Array(xhr.response || xhr.mozResponseArrayBuffer);
          png = new PNG(data);
          if (typeof (canvas != null ? canvas.getContext : void 0) === 'function') {
            png.render(canvas);
          }
          return typeof callback === "function" ? callback(png) : void 0;
        };
        return xhr.send(null);
      };

      APNG_DISPOSE_OP_NONE = 0;

      APNG_DISPOSE_OP_BACKGROUND = 1;

      APNG_DISPOSE_OP_PREVIOUS = 2;

      APNG_BLEND_OP_SOURCE = 0;

      APNG_BLEND_OP_OVER = 1;

      function PNG(data) {
        var chunkSize, colors, palLen, delayDen, delayNum, frame, i, index, key, section, palShort, text, _i, _j, _ref;
        this.data = data;
        this.pos = 8;
        this.palette = [];
        this.imgData = [];
        this.transparency = {};
        this.animation = null;
        this.text = {};
        frame = null;
        while (true) {
          chunkSize = this.readUInt32();
          section = ((function() {
            var _i, _results;
            _results = [];
            for (i = _i = 0; _i < 4; i = ++_i) {
              _results.push(String.fromCharCode(this.data[this.pos++]));
            }
            return _results;
          }).call(this)).join('');
          switch (section) {
            case 'IHDR':
              this.width = this.readUInt32();
              this.height = this.readUInt32();
              this.bits = this.data[this.pos++];
              this.colorType = this.data[this.pos++];
              this.compressionMethod = this.data[this.pos++];
              this.filterMethod = this.data[this.pos++];
              this.interlaceMethod = this.data[this.pos++];
              break;
            case 'acTL':
              this.animation = {
                numFrames: this.readUInt32(),
                numPlays: this.readUInt32() || Infinity,
                frames: []
              };
              break;
            case 'PLTE':
              this.palette = this.read(chunkSize);
              break;
            case 'fcTL':
              if (frame) {
                this.animation.frames.push(frame);
              }
              this.pos += 4;
              frame = {
                width: this.readUInt32(),
                height: this.readUInt32(),
                xOffset: this.readUInt32(),
                yOffset: this.readUInt32()
              };
              delayNum = this.readUInt16();
              delayDen = this.readUInt16() || 100;
              frame.delay = 1000 * delayNum / delayDen;
              frame.disposeOp = this.data[this.pos++];
              frame.blendOp = this.data[this.pos++];
              frame.data = [];
              break;
            case 'IDAT':
            case 'fdAT':
              if (section === 'fdAT') {
                this.pos += 4;
                chunkSize -= 4;
              }
              data = (frame != null ? frame.data : void 0) || this.imgData;
              for (i = _i = 0; 0 <= chunkSize ? _i < chunkSize : _i > chunkSize; i = 0 <= chunkSize ? ++_i : --_i) {
                data.push(this.data[this.pos++]);
              }
              break;
            case 'tRNS':
              this.transparency = {};
              switch (this.colorType) {
                case 3:
              	palLen = this.palette.length/3;
                  this.transparency.indexed = this.read(chunkSize);
                  if(this.transparency.indexed.length > palLen)
                  	throw new Error('More transparent colors than palette size');
                  /*
                   * According to the PNG spec trns should be increased to the same size as palette if shorter
                   */
                  //palShort = 255 - this.transparency.indexed.length;
                  palShort = palLen - this.transparency.indexed.length;
                  if (palShort > 0) {
                    for (i = _j = 0; 0 <= palShort ? _j < palShort : _j > palShort; i = 0 <= palShort ? ++_j : --_j) {
                      this.transparency.indexed.push(255);
                    }
                  }
                  break;
                case 0:
                  this.transparency.grayscale = this.read(chunkSize)[0];
                  break;
                case 2:
                  this.transparency.rgb = this.read(chunkSize);
              }
              break;
            case 'tEXt':
              text = this.read(chunkSize);
              index = text.indexOf(0);
              key = String.fromCharCode.apply(String, text.slice(0, index));
              this.text[key] = String.fromCharCode.apply(String, text.slice(index + 1));
              break;
            case 'IEND':
              if (frame) {
                this.animation.frames.push(frame);
              }
              this.colors = (function() {
                switch (this.colorType) {
                  case 0:
                  case 3:
                  case 4:
                    return 1;
                  case 2:
                  case 6:
                    return 3;
                }
              }).call(this);
              this.hasAlphaChannel = (_ref = this.colorType) === 4 || _ref === 6;
              colors = this.colors + (this.hasAlphaChannel ? 1 : 0);
              this.pixelBitlength = this.bits * colors;
              this.colorSpace = (function() {
                switch (this.colors) {
                  case 1:
                    return 'DeviceGray';
                  case 3:
                    return 'DeviceRGB';
                }
              }).call(this);
              this.imgData = new Uint8Array(this.imgData);
              return;
            default:
              this.pos += chunkSize;
          }
          this.pos += 4;
          if (this.pos > this.data.length) {
            throw new Error("Incomplete or corrupt PNG file");
          }
        }
        return;
      }

      PNG.prototype.read = function(bytes) {
        var i, _i, _results;
        _results = [];
        for (i = _i = 0; 0 <= bytes ? _i < bytes : _i > bytes; i = 0 <= bytes ? ++_i : --_i) {
          _results.push(this.data[this.pos++]);
        }
        return _results;
      };

      PNG.prototype.readUInt32 = function() {
        var b1, b2, b3, b4;
        b1 = this.data[this.pos++] << 24;
        b2 = this.data[this.pos++] << 16;
        b3 = this.data[this.pos++] << 8;
        b4 = this.data[this.pos++];
        return b1 | b2 | b3 | b4;
      };

      PNG.prototype.readUInt16 = function() {
        var b1, b2;
        b1 = this.data[this.pos++] << 8;
        b2 = this.data[this.pos++];
        return b1 | b2;
      };

      PNG.prototype.decodePixels = function(data) {
        var abyte, c, col, i, left, length, p, pa, paeth, pb, pc, pixelBytes, pixels, pos, row, scanlineLength, upper, upperLeft, _i, _j, _k, _l, _m;
        if (data == null) {
          data = this.imgData;
        }
        if (data.length === 0) {
          return new Uint8Array(0);
        }
        data = new FlateStream(data);
        data = data.getBytes();
        pixelBytes = this.pixelBitlength / 8;
        scanlineLength = pixelBytes * this.width;
        pixels = new Uint8Array(scanlineLength * this.height);
        length = data.length;
        row = 0;
        pos = 0;
        c = 0;
        while (pos < length) {
          switch (data[pos++]) {
            case 0:
              for (i = _i = 0; _i < scanlineLength; i = _i += 1) {
                pixels[c++] = data[pos++];
              }
              break;
            case 1:
              for (i = _j = 0; _j < scanlineLength; i = _j += 1) {
                abyte = data[pos++];
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                pixels[c++] = (abyte + left) % 256;
              }
              break;
            case 2:
              for (i = _k = 0; _k < scanlineLength; i = _k += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                upper = row && pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                pixels[c++] = (upper + abyte) % 256;
              }
              break;
            case 3:
              for (i = _l = 0; _l < scanlineLength; i = _l += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                upper = row && pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                pixels[c++] = (abyte + Math.floor((left + upper) / 2)) % 256;
              }
              break;
            case 4:
              for (i = _m = 0; _m < scanlineLength; i = _m += 1) {
                abyte = data[pos++];
                col = (i - (i % pixelBytes)) / pixelBytes;
                left = i < pixelBytes ? 0 : pixels[c - pixelBytes];
                if (row === 0) {
                  upper = upperLeft = 0;
                } else {
                  upper = pixels[(row - 1) * scanlineLength + col * pixelBytes + (i % pixelBytes)];
                  upperLeft = col && pixels[(row - 1) * scanlineLength + (col - 1) * pixelBytes + (i % pixelBytes)];
                }
                p = left + upper - upperLeft;
                pa = Math.abs(p - left);
                pb = Math.abs(p - upper);
                pc = Math.abs(p - upperLeft);
                if (pa <= pb && pa <= pc) {
                  paeth = left;
                } else if (pb <= pc) {
                  paeth = upper;
                } else {
                  paeth = upperLeft;
                }
                pixels[c++] = (abyte + paeth) % 256;
              }
              break;
            default:
              throw new Error("Invalid filter algorithm: " + data[pos - 1]);
          }
          row++;
        }
        return pixels;
      };

      PNG.prototype.decodePalette = function() {
        var c, i, length, palette, pos, ret, transparency, _i, _ref, _ref1;
        palette = this.palette;
        transparency = this.transparency.indexed || [];
        ret = new Uint8Array((transparency.length || 0) + palette.length);
        pos = 0;
        length = palette.length;
        c = 0;
        for (i = _i = 0, _ref = palette.length; _i < _ref; i = _i += 3) {
          ret[pos++] = palette[i];
          ret[pos++] = palette[i + 1];
          ret[pos++] = palette[i + 2];
          ret[pos++] = (_ref1 = transparency[c++]) != null ? _ref1 : 255;
        }
        return ret;
      };

      PNG.prototype.copyToImageData = function(imageData, pixels) {
        var alpha, colors, data, i, input, j, k, length, palette, v, _ref;
        colors = this.colors;
        palette = null;
        alpha = this.hasAlphaChannel;
        if (this.palette.length) {
          palette = (_ref = this._decodedPalette) != null ? _ref : this._decodedPalette = this.decodePalette();
          colors = 4;
          alpha = true;
        }
        data = imageData.data || imageData;
        length = data.length;
        input = palette || pixels;
        i = j = 0;
        if (colors === 1) {
          while (i < length) {
            k = palette ? pixels[i / 4] * 4 : j;
            v = input[k++];
            data[i++] = v;
            data[i++] = v;
            data[i++] = v;
            data[i++] = alpha ? input[k++] : 255;
            j = k;
          }
        } else {
          while (i < length) {
            k = palette ? pixels[i / 4] * 4 : j;
            data[i++] = input[k++];
            data[i++] = input[k++];
            data[i++] = input[k++];
            data[i++] = alpha ? input[k++] : 255;
            j = k;
          }
        }
      };

      PNG.prototype.decode = function() {
        var ret;
        ret = new Uint8Array(this.width * this.height * 4);
        this.copyToImageData(ret, this.decodePixels());
        return ret;
      };

      try {
          scratchCanvas = global.document.createElement('canvas');
          scratchCtx = scratchCanvas.getContext('2d');
      } catch(e) {
          return -1;
      }

      makeImage = function(imageData) {
        var img;
        scratchCtx.width = imageData.width;
        scratchCtx.height = imageData.height;
        scratchCtx.clearRect(0, 0, imageData.width, imageData.height);
        scratchCtx.putImageData(imageData, 0, 0);
        img = new Image;
        img.src = scratchCanvas.toDataURL();
        return img;
      };

      PNG.prototype.decodeFrames = function(ctx) {
        var frame, i, imageData, pixels, _i, _len, _ref, _results;
        if (!this.animation) {
          return;
        }
        _ref = this.animation.frames;
        _results = [];
        for (i = _i = 0, _len = _ref.length; _i < _len; i = ++_i) {
          frame = _ref[i];
          imageData = ctx.createImageData(frame.width, frame.height);
          pixels = this.decodePixels(new Uint8Array(frame.data));
          this.copyToImageData(imageData, pixels);
          frame.imageData = imageData;
          _results.push(frame.image = makeImage(imageData));
        }
        return _results;
      };

      PNG.prototype.renderFrame = function(ctx, number) {
        var frame, frames, prev;
        frames = this.animation.frames;
        frame = frames[number];
        prev = frames[number - 1];
        if (number === 0) {
          ctx.clearRect(0, 0, this.width, this.height);
        }
        if ((prev != null ? prev.disposeOp : void 0) === APNG_DISPOSE_OP_BACKGROUND) {
          ctx.clearRect(prev.xOffset, prev.yOffset, prev.width, prev.height);
        } else if ((prev != null ? prev.disposeOp : void 0) === APNG_DISPOSE_OP_PREVIOUS) {
          ctx.putImageData(prev.imageData, prev.xOffset, prev.yOffset);
        }
        if (frame.blendOp === APNG_BLEND_OP_SOURCE) {
          ctx.clearRect(frame.xOffset, frame.yOffset, frame.width, frame.height);
        }
        return ctx.drawImage(frame.image, frame.xOffset, frame.yOffset);
      };

      PNG.prototype.animate = function(ctx) {
        var doFrame, frameNumber, frames, numFrames, numPlays, _ref,
          _this = this;
        frameNumber = 0;
        _ref = this.animation, numFrames = _ref.numFrames, frames = _ref.frames, numPlays = _ref.numPlays;
        return (doFrame = function() {
          var f, frame;
          f = frameNumber++ % numFrames;
          frame = frames[f];
          _this.renderFrame(ctx, f);
          if (numFrames > 1 && frameNumber / numFrames < numPlays) {
            return _this.animation._timeout = setTimeout(doFrame, frame.delay);
          }
        })();
      };

      PNG.prototype.stopAnimation = function() {
        var _ref;
        return clearTimeout((_ref = this.animation) != null ? _ref._timeout : void 0);
      };

      PNG.prototype.render = function(canvas) {
        var ctx, data;
        if (canvas._png) {
          canvas._png.stopAnimation();
        }
        canvas._png = this;
        canvas.width = this.width;
        canvas.height = this.height;
        ctx = canvas.getContext("2d");
        if (this.animation) {
          this.decodeFrames(ctx);
          return this.animate(ctx);
        } else {
          data = ctx.createImageData(this.width, this.height);
          this.copyToImageData(data, this.decodePixels());
          return ctx.putImageData(data, 0, 0);
        }
      };

      return PNG;

    })();

    global.PNG = PNG;

  })(typeof window !== "undefined" && window || undefined);

  /*
   * Extracted from pdf.js
   * https://github.com/andreasgal/pdf.js
   *
   * Copyright (c) 2011 Mozilla Foundation
   *
   * Contributors: Andreas Gal <gal@mozilla.com>
   *               Chris G Jones <cjones@mozilla.com>
   *               Shaon Barman <shaon.barman@gmail.com>
   *               Vivien Nicolas <21@vingtetun.org>
   *               Justin D'Arcangelo <justindarc@gmail.com>
   *               Yury Delendik
   *
   * 
   */

  var DecodeStream = (function() {
    function constructor() {
      this.pos = 0;
      this.bufferLength = 0;
      this.eof = false;
      this.buffer = null;
    }

    constructor.prototype = {
      ensureBuffer: function decodestream_ensureBuffer(requested) {
        var buffer = this.buffer;
        var current = buffer ? buffer.byteLength : 0;
        if (requested < current)
          return buffer;
        var size = 512;
        while (size < requested)
          size <<= 1;
        var buffer2 = new Uint8Array(size);
        for (var i = 0; i < current; ++i)
          buffer2[i] = buffer[i];
        return this.buffer = buffer2;
      },
      getByte: function decodestream_getByte() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return this.buffer[this.pos++];
      },
      getBytes: function decodestream_getBytes(length) {
        var pos = this.pos;

        if (length) {
          this.ensureBuffer(pos + length);
          var end = pos + length;

          while (!this.eof && this.bufferLength < end)
            this.readBlock();

          var bufEnd = this.bufferLength;
          if (end > bufEnd)
            end = bufEnd;
        } else {
          while (!this.eof)
            this.readBlock();

          var end = this.bufferLength;
        }

        this.pos = end;
        return this.buffer.subarray(pos, end);
      },
      lookChar: function decodestream_lookChar() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return String.fromCharCode(this.buffer[this.pos]);
      },
      getChar: function decodestream_getChar() {
        var pos = this.pos;
        while (this.bufferLength <= pos) {
          if (this.eof)
            return null;
          this.readBlock();
        }
        return String.fromCharCode(this.buffer[this.pos++]);
      },
      makeSubStream: function decodestream_makeSubstream(start, length, dict) {
        var end = start + length;
        while (this.bufferLength <= end && !this.eof)
          this.readBlock();
        return new Stream(this.buffer, start, length, dict);
      },
      skip: function decodestream_skip(n) {
        if (!n)
          n = 1;
        this.pos += n;
      },
      reset: function decodestream_reset() {
        this.pos = 0;
      }
    };

    return constructor;
  })();

  var FlateStream = (function() {
    if (typeof Uint32Array === 'undefined') {
      return undefined;
    }
    var codeLenCodeMap = new Uint32Array([
      16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15
    ]);

    var lengthDecode = new Uint32Array([
      0x00003, 0x00004, 0x00005, 0x00006, 0x00007, 0x00008, 0x00009, 0x0000a,
      0x1000b, 0x1000d, 0x1000f, 0x10011, 0x20013, 0x20017, 0x2001b, 0x2001f,
      0x30023, 0x3002b, 0x30033, 0x3003b, 0x40043, 0x40053, 0x40063, 0x40073,
      0x50083, 0x500a3, 0x500c3, 0x500e3, 0x00102, 0x00102, 0x00102
    ]);

    var distDecode = new Uint32Array([
      0x00001, 0x00002, 0x00003, 0x00004, 0x10005, 0x10007, 0x20009, 0x2000d,
      0x30011, 0x30019, 0x40021, 0x40031, 0x50041, 0x50061, 0x60081, 0x600c1,
      0x70101, 0x70181, 0x80201, 0x80301, 0x90401, 0x90601, 0xa0801, 0xa0c01,
      0xb1001, 0xb1801, 0xc2001, 0xc3001, 0xd4001, 0xd6001
    ]);

    var fixedLitCodeTab = [new Uint32Array([
      0x70100, 0x80050, 0x80010, 0x80118, 0x70110, 0x80070, 0x80030, 0x900c0,
      0x70108, 0x80060, 0x80020, 0x900a0, 0x80000, 0x80080, 0x80040, 0x900e0,
      0x70104, 0x80058, 0x80018, 0x90090, 0x70114, 0x80078, 0x80038, 0x900d0,
      0x7010c, 0x80068, 0x80028, 0x900b0, 0x80008, 0x80088, 0x80048, 0x900f0,
      0x70102, 0x80054, 0x80014, 0x8011c, 0x70112, 0x80074, 0x80034, 0x900c8,
      0x7010a, 0x80064, 0x80024, 0x900a8, 0x80004, 0x80084, 0x80044, 0x900e8,
      0x70106, 0x8005c, 0x8001c, 0x90098, 0x70116, 0x8007c, 0x8003c, 0x900d8,
      0x7010e, 0x8006c, 0x8002c, 0x900b8, 0x8000c, 0x8008c, 0x8004c, 0x900f8,
      0x70101, 0x80052, 0x80012, 0x8011a, 0x70111, 0x80072, 0x80032, 0x900c4,
      0x70109, 0x80062, 0x80022, 0x900a4, 0x80002, 0x80082, 0x80042, 0x900e4,
      0x70105, 0x8005a, 0x8001a, 0x90094, 0x70115, 0x8007a, 0x8003a, 0x900d4,
      0x7010d, 0x8006a, 0x8002a, 0x900b4, 0x8000a, 0x8008a, 0x8004a, 0x900f4,
      0x70103, 0x80056, 0x80016, 0x8011e, 0x70113, 0x80076, 0x80036, 0x900cc,
      0x7010b, 0x80066, 0x80026, 0x900ac, 0x80006, 0x80086, 0x80046, 0x900ec,
      0x70107, 0x8005e, 0x8001e, 0x9009c, 0x70117, 0x8007e, 0x8003e, 0x900dc,
      0x7010f, 0x8006e, 0x8002e, 0x900bc, 0x8000e, 0x8008e, 0x8004e, 0x900fc,
      0x70100, 0x80051, 0x80011, 0x80119, 0x70110, 0x80071, 0x80031, 0x900c2,
      0x70108, 0x80061, 0x80021, 0x900a2, 0x80001, 0x80081, 0x80041, 0x900e2,
      0x70104, 0x80059, 0x80019, 0x90092, 0x70114, 0x80079, 0x80039, 0x900d2,
      0x7010c, 0x80069, 0x80029, 0x900b2, 0x80009, 0x80089, 0x80049, 0x900f2,
      0x70102, 0x80055, 0x80015, 0x8011d, 0x70112, 0x80075, 0x80035, 0x900ca,
      0x7010a, 0x80065, 0x80025, 0x900aa, 0x80005, 0x80085, 0x80045, 0x900ea,
      0x70106, 0x8005d, 0x8001d, 0x9009a, 0x70116, 0x8007d, 0x8003d, 0x900da,
      0x7010e, 0x8006d, 0x8002d, 0x900ba, 0x8000d, 0x8008d, 0x8004d, 0x900fa,
      0x70101, 0x80053, 0x80013, 0x8011b, 0x70111, 0x80073, 0x80033, 0x900c6,
      0x70109, 0x80063, 0x80023, 0x900a6, 0x80003, 0x80083, 0x80043, 0x900e6,
      0x70105, 0x8005b, 0x8001b, 0x90096, 0x70115, 0x8007b, 0x8003b, 0x900d6,
      0x7010d, 0x8006b, 0x8002b, 0x900b6, 0x8000b, 0x8008b, 0x8004b, 0x900f6,
      0x70103, 0x80057, 0x80017, 0x8011f, 0x70113, 0x80077, 0x80037, 0x900ce,
      0x7010b, 0x80067, 0x80027, 0x900ae, 0x80007, 0x80087, 0x80047, 0x900ee,
      0x70107, 0x8005f, 0x8001f, 0x9009e, 0x70117, 0x8007f, 0x8003f, 0x900de,
      0x7010f, 0x8006f, 0x8002f, 0x900be, 0x8000f, 0x8008f, 0x8004f, 0x900fe,
      0x70100, 0x80050, 0x80010, 0x80118, 0x70110, 0x80070, 0x80030, 0x900c1,
      0x70108, 0x80060, 0x80020, 0x900a1, 0x80000, 0x80080, 0x80040, 0x900e1,
      0x70104, 0x80058, 0x80018, 0x90091, 0x70114, 0x80078, 0x80038, 0x900d1,
      0x7010c, 0x80068, 0x80028, 0x900b1, 0x80008, 0x80088, 0x80048, 0x900f1,
      0x70102, 0x80054, 0x80014, 0x8011c, 0x70112, 0x80074, 0x80034, 0x900c9,
      0x7010a, 0x80064, 0x80024, 0x900a9, 0x80004, 0x80084, 0x80044, 0x900e9,
      0x70106, 0x8005c, 0x8001c, 0x90099, 0x70116, 0x8007c, 0x8003c, 0x900d9,
      0x7010e, 0x8006c, 0x8002c, 0x900b9, 0x8000c, 0x8008c, 0x8004c, 0x900f9,
      0x70101, 0x80052, 0x80012, 0x8011a, 0x70111, 0x80072, 0x80032, 0x900c5,
      0x70109, 0x80062, 0x80022, 0x900a5, 0x80002, 0x80082, 0x80042, 0x900e5,
      0x70105, 0x8005a, 0x8001a, 0x90095, 0x70115, 0x8007a, 0x8003a, 0x900d5,
      0x7010d, 0x8006a, 0x8002a, 0x900b5, 0x8000a, 0x8008a, 0x8004a, 0x900f5,
      0x70103, 0x80056, 0x80016, 0x8011e, 0x70113, 0x80076, 0x80036, 0x900cd,
      0x7010b, 0x80066, 0x80026, 0x900ad, 0x80006, 0x80086, 0x80046, 0x900ed,
      0x70107, 0x8005e, 0x8001e, 0x9009d, 0x70117, 0x8007e, 0x8003e, 0x900dd,
      0x7010f, 0x8006e, 0x8002e, 0x900bd, 0x8000e, 0x8008e, 0x8004e, 0x900fd,
      0x70100, 0x80051, 0x80011, 0x80119, 0x70110, 0x80071, 0x80031, 0x900c3,
      0x70108, 0x80061, 0x80021, 0x900a3, 0x80001, 0x80081, 0x80041, 0x900e3,
      0x70104, 0x80059, 0x80019, 0x90093, 0x70114, 0x80079, 0x80039, 0x900d3,
      0x7010c, 0x80069, 0x80029, 0x900b3, 0x80009, 0x80089, 0x80049, 0x900f3,
      0x70102, 0x80055, 0x80015, 0x8011d, 0x70112, 0x80075, 0x80035, 0x900cb,
      0x7010a, 0x80065, 0x80025, 0x900ab, 0x80005, 0x80085, 0x80045, 0x900eb,
      0x70106, 0x8005d, 0x8001d, 0x9009b, 0x70116, 0x8007d, 0x8003d, 0x900db,
      0x7010e, 0x8006d, 0x8002d, 0x900bb, 0x8000d, 0x8008d, 0x8004d, 0x900fb,
      0x70101, 0x80053, 0x80013, 0x8011b, 0x70111, 0x80073, 0x80033, 0x900c7,
      0x70109, 0x80063, 0x80023, 0x900a7, 0x80003, 0x80083, 0x80043, 0x900e7,
      0x70105, 0x8005b, 0x8001b, 0x90097, 0x70115, 0x8007b, 0x8003b, 0x900d7,
      0x7010d, 0x8006b, 0x8002b, 0x900b7, 0x8000b, 0x8008b, 0x8004b, 0x900f7,
      0x70103, 0x80057, 0x80017, 0x8011f, 0x70113, 0x80077, 0x80037, 0x900cf,
      0x7010b, 0x80067, 0x80027, 0x900af, 0x80007, 0x80087, 0x80047, 0x900ef,
      0x70107, 0x8005f, 0x8001f, 0x9009f, 0x70117, 0x8007f, 0x8003f, 0x900df,
      0x7010f, 0x8006f, 0x8002f, 0x900bf, 0x8000f, 0x8008f, 0x8004f, 0x900ff
    ]), 9];

    var fixedDistCodeTab = [new Uint32Array([
      0x50000, 0x50010, 0x50008, 0x50018, 0x50004, 0x50014, 0x5000c, 0x5001c,
      0x50002, 0x50012, 0x5000a, 0x5001a, 0x50006, 0x50016, 0x5000e, 0x00000,
      0x50001, 0x50011, 0x50009, 0x50019, 0x50005, 0x50015, 0x5000d, 0x5001d,
      0x50003, 0x50013, 0x5000b, 0x5001b, 0x50007, 0x50017, 0x5000f, 0x00000
    ]), 5];
    
    function error(e) {
        throw new Error(e)
    }

    function constructor(bytes) {
      //var bytes = stream.getBytes();
      var bytesPos = 0;

      var cmf = bytes[bytesPos++];
      var flg = bytes[bytesPos++];
      if (cmf == -1 || flg == -1)
        error('Invalid header in flate stream');
      if ((cmf & 0x0f) != 0x08)
        error('Unknown compression method in flate stream');
      if ((((cmf << 8) + flg) % 31) != 0)
        error('Bad FCHECK in flate stream');
      if (flg & 0x20)
        error('FDICT bit set in flate stream');

      this.bytes = bytes;
      this.bytesPos = bytesPos;

      this.codeSize = 0;
      this.codeBuf = 0;

      DecodeStream.call(this);
    }

    constructor.prototype = Object.create(DecodeStream.prototype);

    constructor.prototype.getBits = function(bits) {
      var codeSize = this.codeSize;
      var codeBuf = this.codeBuf;
      var bytes = this.bytes;
      var bytesPos = this.bytesPos;

      var b;
      while (codeSize < bits) {
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad encoding in flate stream');
        codeBuf |= b << codeSize;
        codeSize += 8;
      }
      b = codeBuf & ((1 << bits) - 1);
      this.codeBuf = codeBuf >> bits;
      this.codeSize = codeSize -= bits;
      this.bytesPos = bytesPos;
      return b;
    };

    constructor.prototype.getCode = function(table) {
      var codes = table[0];
      var maxLen = table[1];
      var codeSize = this.codeSize;
      var codeBuf = this.codeBuf;
      var bytes = this.bytes;
      var bytesPos = this.bytesPos;

      while (codeSize < maxLen) {
        var b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad encoding in flate stream');
        codeBuf |= (b << codeSize);
        codeSize += 8;
      }
      var code = codes[codeBuf & ((1 << maxLen) - 1)];
      var codeLen = code >> 16;
      var codeVal = code & 0xffff;
      if (codeSize == 0 || codeSize < codeLen || codeLen == 0)
        error('Bad encoding in flate stream');
      this.codeBuf = (codeBuf >> codeLen);
      this.codeSize = (codeSize - codeLen);
      this.bytesPos = bytesPos;
      return codeVal;
    };

    constructor.prototype.generateHuffmanTable = function(lengths) {
      var n = lengths.length;

      // find max code length
      var maxLen = 0;
      for (var i = 0; i < n; ++i) {
        if (lengths[i] > maxLen)
          maxLen = lengths[i];
      }

      // build the table
      var size = 1 << maxLen;
      var codes = new Uint32Array(size);
      for (var len = 1, code = 0, skip = 2;
           len <= maxLen;
           ++len, code <<= 1, skip <<= 1) {
        for (var val = 0; val < n; ++val) {
          if (lengths[val] == len) {
            // bit-reverse the code
            var code2 = 0;
            var t = code;
            for (var i = 0; i < len; ++i) {
              code2 = (code2 << 1) | (t & 1);
              t >>= 1;
            }

            // fill the table entries
            for (var i = code2; i < size; i += skip)
              codes[i] = (len << 16) | val;

            ++code;
          }
        }
      }

      return [codes, maxLen];
    };

    constructor.prototype.readBlock = function() {
      function repeat(stream, array, len, offset, what) {
        var repeat = stream.getBits(len) + offset;
        while (repeat-- > 0)
          array[i++] = what;
      }

      // read block header
      var hdr = this.getBits(3);
      if (hdr & 1)
        this.eof = true;
      hdr >>= 1;

      if (hdr == 0) { // uncompressed block
        var bytes = this.bytes;
        var bytesPos = this.bytesPos;
        var b;

        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        var blockLen = b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        blockLen |= (b << 8);
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        var check = b;
        if (typeof (b = bytes[bytesPos++]) == 'undefined')
          error('Bad block header in flate stream');
        check |= (b << 8);
        if (check != (~blockLen & 0xffff))
          error('Bad uncompressed block length in flate stream');

        this.codeBuf = 0;
        this.codeSize = 0;

        var bufferLength = this.bufferLength;
        var buffer = this.ensureBuffer(bufferLength + blockLen);
        var end = bufferLength + blockLen;
        this.bufferLength = end;
        for (var n = bufferLength; n < end; ++n) {
          if (typeof (b = bytes[bytesPos++]) == 'undefined') {
            this.eof = true;
            break;
          }
          buffer[n] = b;
        }
        this.bytesPos = bytesPos;
        return;
      }

      var litCodeTable;
      var distCodeTable;
      if (hdr == 1) { // compressed block, fixed codes
        litCodeTable = fixedLitCodeTab;
        distCodeTable = fixedDistCodeTab;
      } else if (hdr == 2) { // compressed block, dynamic codes
        var numLitCodes = this.getBits(5) + 257;
        var numDistCodes = this.getBits(5) + 1;
        var numCodeLenCodes = this.getBits(4) + 4;

        // build the code lengths code table
        var codeLenCodeLengths = Array(codeLenCodeMap.length);
        var i = 0;
        while (i < numCodeLenCodes)
          codeLenCodeLengths[codeLenCodeMap[i++]] = this.getBits(3);
        var codeLenCodeTab = this.generateHuffmanTable(codeLenCodeLengths);

        // build the literal and distance code tables
        var len = 0;
        var i = 0;
        var codes = numLitCodes + numDistCodes;
        var codeLengths = new Array(codes);
        while (i < codes) {
          var code = this.getCode(codeLenCodeTab);
          if (code == 16) {
            repeat(this, codeLengths, 2, 3, len);
          } else if (code == 17) {
            repeat(this, codeLengths, 3, 3, len = 0);
          } else if (code == 18) {
            repeat(this, codeLengths, 7, 11, len = 0);
          } else {
            codeLengths[i++] = len = code;
          }
        }

        litCodeTable =
          this.generateHuffmanTable(codeLengths.slice(0, numLitCodes));
        distCodeTable =
          this.generateHuffmanTable(codeLengths.slice(numLitCodes, codes));
      } else {
        error('Unknown block type in flate stream');
      }

      var buffer = this.buffer;
      var limit = buffer ? buffer.length : 0;
      var pos = this.bufferLength;
      while (true) {
        var code1 = this.getCode(litCodeTable);
        if (code1 < 256) {
          if (pos + 1 >= limit) {
            buffer = this.ensureBuffer(pos + 1);
            limit = buffer.length;
          }
          buffer[pos++] = code1;
          continue;
        }
        if (code1 == 256) {
          this.bufferLength = pos;
          return;
        }
        code1 -= 257;
        code1 = lengthDecode[code1];
        var code2 = code1 >> 16;
        if (code2 > 0)
          code2 = this.getBits(code2);
        var len = (code1 & 0xffff) + code2;
        code1 = this.getCode(distCodeTable);
        code1 = distDecode[code1];
        code2 = code1 >> 16;
        if (code2 > 0)
          code2 = this.getBits(code2);
        var dist = (code1 & 0xffff) + code2;
        if (pos + len >= limit) {
          buffer = this.ensureBuffer(pos + len);
          limit = buffer.length;
        }
        for (var k = 0; k < len; ++k, ++pos)
          buffer[pos] = buffer[pos - dist];
      }
    };

    return constructor;
  })();

  /**
   * JavaScript Polyfill functions for jsPDF
   * Collected from public resources by
   * https://github.com/diegocr
   */

  (function (global) {
  	var b64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

  	if (typeof global.btoa === 'undefined') {
  		global.btoa = function(data) {
  			//  discuss at: http://phpjs.org/functions/base64_encode/
  			// original by: Tyler Akins (http://rumkin.com)
  			// improved by: Bayron Guevara
  			// improved by: Thunder.m
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Rafal Kukawski (http://kukawski.pl)
  			// bugfixed by: Pellentesque Malesuada
  			//   example 1: base64_encode('Kevin van Zonneveld');
  			//   returns 1: 'S2V2aW4gdmFuIFpvbm5ldmVsZA=='

  			var o1,o2,o3,h1,h2,h3,h4,bits,i = 0,ac = 0,enc = '',tmp_arr = [];

  			if (!data) {
  				return data;
  			}

  			do { // pack three octets into four hexets
  				o1 = data.charCodeAt(i++);
  				o2 = data.charCodeAt(i++);
  				o3 = data.charCodeAt(i++);

  				bits = o1 << 16 | o2 << 8 | o3;

  				h1 = bits >> 18 & 0x3f;
  				h2 = bits >> 12 & 0x3f;
  				h3 = bits >> 6 & 0x3f;
  				h4 = bits & 0x3f;

  				// use hexets to index into b64, and append result to encoded string
  				tmp_arr[ac++] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4);
  			} while (i < data.length);

  			enc = tmp_arr.join('');

  			var r = data.length % 3;

  			return (r ? enc.slice(0, r - 3) : enc) + '==='.slice(r || 3);
  		};
  	}

  	if (typeof global.atob === 'undefined') {
  		global.atob = function(data) {
  			//  discuss at: http://phpjs.org/functions/base64_decode/
  			// original by: Tyler Akins (http://rumkin.com)
  			// improved by: Thunder.m
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			// improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			//    input by: Aman Gupta
  			//    input by: Brett Zamir (http://brett-zamir.me)
  			// bugfixed by: Onno Marsman
  			// bugfixed by: Pellentesque Malesuada
  			// bugfixed by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  			//   example 1: base64_decode('S2V2aW4gdmFuIFpvbm5ldmVsZA==');
  			//   returns 1: 'Kevin van Zonneveld'

  			var o1,o2,o3,h1,h2,h3,h4,bits,i = 0,ac = 0,dec = '',tmp_arr = [];

  			if (!data) {
  				return data;
  			}

  			data += '';

  			do { // unpack four hexets into three octets using index points in b64
  				h1 = b64.indexOf(data.charAt(i++));
  				h2 = b64.indexOf(data.charAt(i++));
  				h3 = b64.indexOf(data.charAt(i++));
  				h4 = b64.indexOf(data.charAt(i++));

  				bits = h1 << 18 | h2 << 12 | h3 << 6 | h4;

  				o1 = bits >> 16 & 0xff;
  				o2 = bits >> 8 & 0xff;
  				o3 = bits & 0xff;

  				if (h3 == 64) {
  					tmp_arr[ac++] = String.fromCharCode(o1);
  				} else if (h4 == 64) {
  					tmp_arr[ac++] = String.fromCharCode(o1, o2);
  				} else {
  					tmp_arr[ac++] = String.fromCharCode(o1, o2, o3);
  				}
  			} while (i < data.length);

  			dec = tmp_arr.join('');

  			return dec;
  		};
  	}

  	if (!Array.prototype.map) {
  		Array.prototype.map = function(fun /*, thisArg */) {
  			if (this === void 0 || this === null || typeof fun !== "function")
  				throw new TypeError();

  			var t = Object(this), len = t.length >>> 0, res = new Array(len);
  			var thisArg = arguments.length > 1 ? arguments[1] : void 0;
  			for (var i = 0; i < len; i++) {
  				// NOTE: Absolute correctness would demand Object.defineProperty
  				//       be used.  But this method is fairly new, and failure is
  				//       possible only if Object.prototype or Array.prototype
  				//       has a property |i| (very unlikely), so use a less-correct
  				//       but more portable alternative.
  				if (i in t)
  					res[i] = fun.call(thisArg, t[i], i, t);
  			}

  			return res;
  		};
  	}


  	if(!Array.isArray) {
  		Array.isArray = function(arg) {
  			return Object.prototype.toString.call(arg) === '[object Array]';
  		};
  	}

  	if (!Array.prototype.forEach) {
  		Array.prototype.forEach = function(fun, thisArg) {
  			"use strict";

  			if (this === void 0 || this === null || typeof fun !== "function")
  				throw new TypeError();

  			var t = Object(this), len = t.length >>> 0;
  			for (var i = 0; i < len; i++) {
  				if (i in t)
  					fun.call(thisArg, t[i], i, t);
  			}
  		};
  	}

  	if (!Object.keys) {
  		Object.keys = (function () {
  			'use strict';

  			var hasOwnProperty = Object.prototype.hasOwnProperty,
  				hasDontEnumBug = !({toString: null}).propertyIsEnumerable('toString'),
  				dontEnums = ['toString','toLocaleString','valueOf','hasOwnProperty',
  					'isPrototypeOf','propertyIsEnumerable','constructor'],
  				dontEnumsLength = dontEnums.length;

  			return function (obj) {
  				if (typeof obj !== 'object' && (typeof obj !== 'function' || obj === null)) {
  					throw new TypeError();
  				}
  				var result = [], prop, i;

  				for (prop in obj) {
  					if (hasOwnProperty.call(obj, prop)) {
  						result.push(prop);
  					}
  				}

  				if (hasDontEnumBug) {
  					for (i = 0; i < dontEnumsLength; i++) {
  						if (hasOwnProperty.call(obj, dontEnums[i])) {
  							result.push(dontEnums[i]);
  						}
  					}
  				}
  				return result;
  			};
  		}());
  	}

  	if (!String.prototype.trim) {
  		String.prototype.trim = function () {
  			return this.replace(/^\s+|\s+$/g, '');
  		};
  	}
  	if (!String.prototype.trimLeft) {
  		String.prototype.trimLeft = function() {
  			return this.replace(/^\s+/g, "");
  		};
  	}
  	if (!String.prototype.trimRight) {
  		String.prototype.trimRight = function() {
  			return this.replace(/\s+$/g, "");
  		};
  	}

  })(typeof self !== "undefined" && self || typeof window !== "undefined" && window || undefined);

  var jsPDF = jsPDF;

  return jsPDF;

}));
/*!

JSZip v3.10.0 - A JavaScript class for generating and reading zip files
<http://stuartk.com/jszip>

(c) 2009-2016 Stuart Knightley <stuart [at] stuartk.com>
Dual licenced under the MIT license or GPLv3. See https://raw.github.com/Stuk/jszip/main/LICENSE.markdown.

JSZip uses the library pako released under the MIT license :
https://github.com/nodeca/pako/blob/main/LICENSE
*/

(function(f){if(typeof exports==="object"&&typeof module!=="undefined"){module.exports=f()}else if(typeof define==="function"&&define.amd){define([],f)}else{var g;if(typeof window!=="undefined"){g=window}else if(typeof global!=="undefined"){g=global}else if(typeof self!=="undefined"){g=self}else{g=this}g.JSZip = f()}})(function(){var define,module,exports;return (function e(t,n,r){function s(o,u){if(!n[o]){if(!t[o]){var a=typeof require=="function"&&require;if(!u&&a)return a(o,!0);if(i)return i(o,!0);var f=new Error("Cannot find module '"+o+"'");throw f.code="MODULE_NOT_FOUND",f}var l=n[o]={exports:{}};t[o][0].call(l.exports,function(e){var n=t[o][1][e];return s(n?n:e)},l,l.exports,e,t,n,r)}return n[o].exports}var i=typeof require=="function"&&require;for(var o=0;o<r.length;o++)s(r[o]);return s})({1:[function(require,module,exports){
'use strict';
var utils = require('./utils');
var support = require('./support');
// private property
var _keyStr = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";


// public method for encoding
exports.encode = function(input) {
    var output = [];
    var chr1, chr2, chr3, enc1, enc2, enc3, enc4;
    var i = 0, len = input.length, remainingBytes = len;

    var isArray = utils.getTypeOf(input) !== "string";
    while (i < input.length) {
        remainingBytes = len - i;

        if (!isArray) {
            chr1 = input.charCodeAt(i++);
            chr2 = i < len ? input.charCodeAt(i++) : 0;
            chr3 = i < len ? input.charCodeAt(i++) : 0;
        } else {
            chr1 = input[i++];
            chr2 = i < len ? input[i++] : 0;
            chr3 = i < len ? input[i++] : 0;
        }

        enc1 = chr1 >> 2;
        enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
        enc3 = remainingBytes > 1 ? (((chr2 & 15) << 2) | (chr3 >> 6)) : 64;
        enc4 = remainingBytes > 2 ? (chr3 & 63) : 64;

        output.push(_keyStr.charAt(enc1) + _keyStr.charAt(enc2) + _keyStr.charAt(enc3) + _keyStr.charAt(enc4));

    }

    return output.join("");
};

// public method for decoding
exports.decode = function(input) {
    var chr1, chr2, chr3;
    var enc1, enc2, enc3, enc4;
    var i = 0, resultIndex = 0;

    var dataUrlPrefix = "data:";

    if (input.substr(0, dataUrlPrefix.length) === dataUrlPrefix) {
        // This is a common error: people give a data url
        // (data:image/png;base64,iVBOR...) with a {base64: true} and
        // wonders why things don't work.
        // We can detect that the string input looks like a data url but we
        // *can't* be sure it is one: removing everything up to the comma would
        // be too dangerous.
        throw new Error("Invalid base64 input, it looks like a data url.");
    }

    input = input.replace(/[^A-Za-z0-9\+\/\=]/g, "");

    var totalLength = input.length * 3 / 4;
    if(input.charAt(input.length - 1) === _keyStr.charAt(64)) {
        totalLength--;
    }
    if(input.charAt(input.length - 2) === _keyStr.charAt(64)) {
        totalLength--;
    }
    if (totalLength % 1 !== 0) {
        // totalLength is not an integer, the length does not match a valid
        // base64 content. That can happen if:
        // - the input is not a base64 content
        // - the input is *almost* a base64 content, with a extra chars at the
        //   beginning or at the end
        // - the input uses a base64 variant (base64url for example)
        throw new Error("Invalid base64 input, bad content length.");
    }
    var output;
    if (support.uint8array) {
        output = new Uint8Array(totalLength|0);
    } else {
        output = new Array(totalLength|0);
    }

    while (i < input.length) {

        enc1 = _keyStr.indexOf(input.charAt(i++));
        enc2 = _keyStr.indexOf(input.charAt(i++));
        enc3 = _keyStr.indexOf(input.charAt(i++));
        enc4 = _keyStr.indexOf(input.charAt(i++));

        chr1 = (enc1 << 2) | (enc2 >> 4);
        chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
        chr3 = ((enc3 & 3) << 6) | enc4;

        output[resultIndex++] = chr1;

        if (enc3 !== 64) {
            output[resultIndex++] = chr2;
        }
        if (enc4 !== 64) {
            output[resultIndex++] = chr3;
        }

    }

    return output;
};

},{"./support":30,"./utils":32}],2:[function(require,module,exports){
'use strict';

var external = require("./external");
var DataWorker = require('./stream/DataWorker');
var Crc32Probe = require('./stream/Crc32Probe');
var DataLengthProbe = require('./stream/DataLengthProbe');

/**
 * Represent a compressed object, with everything needed to decompress it.
 * @constructor
 * @param {number} compressedSize the size of the data compressed.
 * @param {number} uncompressedSize the size of the data after decompression.
 * @param {number} crc32 the crc32 of the decompressed file.
 * @param {object} compression the type of compression, see lib/compressions.js.
 * @param {String|ArrayBuffer|Uint8Array|Buffer} data the compressed data.
 */
function CompressedObject(compressedSize, uncompressedSize, crc32, compression, data) {
    this.compressedSize = compressedSize;
    this.uncompressedSize = uncompressedSize;
    this.crc32 = crc32;
    this.compression = compression;
    this.compressedContent = data;
}

CompressedObject.prototype = {
    /**
     * Create a worker to get the uncompressed content.
     * @return {GenericWorker} the worker.
     */
    getContentWorker: function () {
        var worker = new DataWorker(external.Promise.resolve(this.compressedContent))
            .pipe(this.compression.uncompressWorker())
            .pipe(new DataLengthProbe("data_length"));

        var that = this;
        worker.on("end", function () {
            if (this.streamInfo['data_length'] !== that.uncompressedSize) {
                throw new Error("Bug : uncompressed data size mismatch");
            }
        });
        return worker;
    },
    /**
     * Create a worker to get the compressed content.
     * @return {GenericWorker} the worker.
     */
    getCompressedWorker: function () {
        return new DataWorker(external.Promise.resolve(this.compressedContent))
            .withStreamInfo("compressedSize", this.compressedSize)
            .withStreamInfo("uncompressedSize", this.uncompressedSize)
            .withStreamInfo("crc32", this.crc32)
            .withStreamInfo("compression", this.compression)
            ;
    }
};

/**
 * Chain the given worker with other workers to compress the content with the
 * given compression.
 * @param {GenericWorker} uncompressedWorker the worker to pipe.
 * @param {Object} compression the compression object.
 * @param {Object} compressionOptions the options to use when compressing.
 * @return {GenericWorker} the new worker compressing the content.
 */
CompressedObject.createWorkerFrom = function (uncompressedWorker, compression, compressionOptions) {
    return uncompressedWorker
        .pipe(new Crc32Probe())
        .pipe(new DataLengthProbe("uncompressedSize"))
        .pipe(compression.compressWorker(compressionOptions))
        .pipe(new DataLengthProbe("compressedSize"))
        .withStreamInfo("compression", compression);
};

module.exports = CompressedObject;

},{"./external":6,"./stream/Crc32Probe":25,"./stream/DataLengthProbe":26,"./stream/DataWorker":27}],3:[function(require,module,exports){
'use strict';

var GenericWorker = require("./stream/GenericWorker");

exports.STORE = {
    magic: "\x00\x00",
    compressWorker : function (compressionOptions) {
        return new GenericWorker("STORE compression");
    },
    uncompressWorker : function () {
        return new GenericWorker("STORE decompression");
    }
};
exports.DEFLATE = require('./flate');

},{"./flate":7,"./stream/GenericWorker":28}],4:[function(require,module,exports){
'use strict';

var utils = require('./utils');

/**
 * The following functions come from pako, from pako/lib/zlib/crc32.js
 * released under the MIT license, see pako https://github.com/nodeca/pako/
 */

// Use ordinary array, since untyped makes no boost here
function makeTable() {
    var c, table = [];

    for(var n =0; n < 256; n++){
        c = n;
        for(var k =0; k < 8; k++){
            c = ((c&1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
        }
        table[n] = c;
    }

    return table;
}

// Create table on load. Just 255 signed longs. Not a problem.
var crcTable = makeTable();


function crc32(crc, buf, len, pos) {
    var t = crcTable, end = pos + len;

    crc = crc ^ (-1);

    for (var i = pos; i < end; i++ ) {
        crc = (crc >>> 8) ^ t[(crc ^ buf[i]) & 0xFF];
    }

    return (crc ^ (-1)); // >>> 0;
}

// That's all for the pako functions.

/**
 * Compute the crc32 of a string.
 * This is almost the same as the function crc32, but for strings. Using the
 * same function for the two use cases leads to horrible performances.
 * @param {Number} crc the starting value of the crc.
 * @param {String} str the string to use.
 * @param {Number} len the length of the string.
 * @param {Number} pos the starting position for the crc32 computation.
 * @return {Number} the computed crc32.
 */
function crc32str(crc, str, len, pos) {
    var t = crcTable, end = pos + len;

    crc = crc ^ (-1);

    for (var i = pos; i < end; i++ ) {
        crc = (crc >>> 8) ^ t[(crc ^ str.charCodeAt(i)) & 0xFF];
    }

    return (crc ^ (-1)); // >>> 0;
}

module.exports = function crc32wrapper(input, crc) {
    if (typeof input === "undefined" || !input.length) {
        return 0;
    }

    var isArray = utils.getTypeOf(input) !== "string";

    if(isArray) {
        return crc32(crc|0, input, input.length, 0);
    } else {
        return crc32str(crc|0, input, input.length, 0);
    }
};

},{"./utils":32}],5:[function(require,module,exports){
'use strict';
exports.base64 = false;
exports.binary = false;
exports.dir = false;
exports.createFolders = true;
exports.date = null;
exports.compression = null;
exports.compressionOptions = null;
exports.comment = null;
exports.unixPermissions = null;
exports.dosPermissions = null;

},{}],6:[function(require,module,exports){
/* global Promise */
'use strict';

// load the global object first:
// - it should be better integrated in the system (unhandledRejection in node)
// - the environment may have a custom Promise implementation (see zone.js)
var ES6Promise = null;
if (typeof Promise !== "undefined") {
    ES6Promise = Promise;
} else {
    ES6Promise = require("lie");
}

/**
 * Let the user use/change some implementations.
 */
module.exports = {
    Promise: ES6Promise
};

},{"lie":37}],7:[function(require,module,exports){
'use strict';
var USE_TYPEDARRAY = (typeof Uint8Array !== 'undefined') && (typeof Uint16Array !== 'undefined') && (typeof Uint32Array !== 'undefined');

var pako = require("pako");
var utils = require("./utils");
var GenericWorker = require("./stream/GenericWorker");

var ARRAY_TYPE = USE_TYPEDARRAY ? "uint8array" : "array";

exports.magic = "\x08\x00";

/**
 * Create a worker that uses pako to inflate/deflate.
 * @constructor
 * @param {String} action the name of the pako function to call : either "Deflate" or "Inflate".
 * @param {Object} options the options to use when (de)compressing.
 */
function FlateWorker(action, options) {
    GenericWorker.call(this, "FlateWorker/" + action);

    this._pako = null;
    this._pakoAction = action;
    this._pakoOptions = options;
    // the `meta` object from the last chunk received
    // this allow this worker to pass around metadata
    this.meta = {};
}

utils.inherits(FlateWorker, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
FlateWorker.prototype.processChunk = function (chunk) {
    this.meta = chunk.meta;
    if (this._pako === null) {
        this._createPako();
    }
    this._pako.push(utils.transformTo(ARRAY_TYPE, chunk.data), false);
};

/**
 * @see GenericWorker.flush
 */
FlateWorker.prototype.flush = function () {
    GenericWorker.prototype.flush.call(this);
    if (this._pako === null) {
        this._createPako();
    }
    this._pako.push([], true);
};
/**
 * @see GenericWorker.cleanUp
 */
FlateWorker.prototype.cleanUp = function () {
    GenericWorker.prototype.cleanUp.call(this);
    this._pako = null;
};

/**
 * Create the _pako object.
 * TODO: lazy-loading this object isn't the best solution but it's the
 * quickest. The best solution is to lazy-load the worker list. See also the
 * issue #446.
 */
FlateWorker.prototype._createPako = function () {
    this._pako = new pako[this._pakoAction]({
        raw: true,
        level: this._pakoOptions.level || -1 // default compression
    });
    var self = this;
    this._pako.onData = function(data) {
        self.push({
            data : data,
            meta : self.meta
        });
    };
};

exports.compressWorker = function (compressionOptions) {
    return new FlateWorker("Deflate", compressionOptions);
};
exports.uncompressWorker = function () {
    return new FlateWorker("Inflate", {});
};

},{"./stream/GenericWorker":28,"./utils":32,"pako":38}],8:[function(require,module,exports){
'use strict';

var utils = require('../utils');
var GenericWorker = require('../stream/GenericWorker');
var utf8 = require('../utf8');
var crc32 = require('../crc32');
var signature = require('../signature');

/**
 * Transform an integer into a string in hexadecimal.
 * @private
 * @param {number} dec the number to convert.
 * @param {number} bytes the number of bytes to generate.
 * @returns {string} the result.
 */
var decToHex = function(dec, bytes) {
    var hex = "", i;
    for (i = 0; i < bytes; i++) {
        hex += String.fromCharCode(dec & 0xff);
        dec = dec >>> 8;
    }
    return hex;
};

/**
 * Generate the UNIX part of the external file attributes.
 * @param {Object} unixPermissions the unix permissions or null.
 * @param {Boolean} isDir true if the entry is a directory, false otherwise.
 * @return {Number} a 32 bit integer.
 *
 * adapted from http://unix.stackexchange.com/questions/14705/the-zip-formats-external-file-attribute :
 *
 * TTTTsstrwxrwxrwx0000000000ADVSHR
 * ^^^^____________________________ file type, see zipinfo.c (UNX_*)
 *     ^^^_________________________ setuid, setgid, sticky
 *        ^^^^^^^^^________________ permissions
 *                 ^^^^^^^^^^______ not used ?
 *                           ^^^^^^ DOS attribute bits : Archive, Directory, Volume label, System file, Hidden, Read only
 */
var generateUnixExternalFileAttr = function (unixPermissions, isDir) {

    var result = unixPermissions;
    if (!unixPermissions) {
        // I can't use octal values in strict mode, hence the hexa.
        //  040775 => 0x41fd
        // 0100664 => 0x81b4
        result = isDir ? 0x41fd : 0x81b4;
    }
    return (result & 0xFFFF) << 16;
};

/**
 * Generate the DOS part of the external file attributes.
 * @param {Object} dosPermissions the dos permissions or null.
 * @param {Boolean} isDir true if the entry is a directory, false otherwise.
 * @return {Number} a 32 bit integer.
 *
 * Bit 0     Read-Only
 * Bit 1     Hidden
 * Bit 2     System
 * Bit 3     Volume Label
 * Bit 4     Directory
 * Bit 5     Archive
 */
var generateDosExternalFileAttr = function (dosPermissions, isDir) {

    // the dir flag is already set for compatibility
    return (dosPermissions || 0)  & 0x3F;
};

/**
 * Generate the various parts used in the construction of the final zip file.
 * @param {Object} streamInfo the hash with information about the compressed file.
 * @param {Boolean} streamedContent is the content streamed ?
 * @param {Boolean} streamingEnded is the stream finished ?
 * @param {number} offset the current offset from the start of the zip file.
 * @param {String} platform let's pretend we are this platform (change platform dependents fields)
 * @param {Function} encodeFileName the function to encode the file name / comment.
 * @return {Object} the zip parts.
 */
var generateZipParts = function(streamInfo, streamedContent, streamingEnded, offset, platform, encodeFileName) {
    var file = streamInfo['file'],
    compression = streamInfo['compression'],
    useCustomEncoding = encodeFileName !== utf8.utf8encode,
    encodedFileName = utils.transformTo("string", encodeFileName(file.name)),
    utfEncodedFileName = utils.transformTo("string", utf8.utf8encode(file.name)),
    comment = file.comment,
    encodedComment = utils.transformTo("string", encodeFileName(comment)),
    utfEncodedComment = utils.transformTo("string", utf8.utf8encode(comment)),
    useUTF8ForFileName = utfEncodedFileName.length !== file.name.length,
    useUTF8ForComment = utfEncodedComment.length !== comment.length,
    dosTime,
    dosDate,
    extraFields = "",
    unicodePathExtraField = "",
    unicodeCommentExtraField = "",
    dir = file.dir,
    date = file.date;


    var dataInfo = {
        crc32 : 0,
        compressedSize : 0,
        uncompressedSize : 0
    };

    // if the content is streamed, the sizes/crc32 are only available AFTER
    // the end of the stream.
    if (!streamedContent || streamingEnded) {
        dataInfo.crc32 = streamInfo['crc32'];
        dataInfo.compressedSize = streamInfo['compressedSize'];
        dataInfo.uncompressedSize = streamInfo['uncompressedSize'];
    }

    var bitflag = 0;
    if (streamedContent) {
        // Bit 3: the sizes/crc32 are set to zero in the local header.
        // The correct values are put in the data descriptor immediately
        // following the compressed data.
        bitflag |= 0x0008;
    }
    if (!useCustomEncoding && (useUTF8ForFileName || useUTF8ForComment)) {
        // Bit 11: Language encoding flag (EFS).
        bitflag |= 0x0800;
    }


    var extFileAttr = 0;
    var versionMadeBy = 0;
    if (dir) {
        // dos or unix, we set the dos dir flag
        extFileAttr |= 0x00010;
    }
    if(platform === "UNIX") {
        versionMadeBy = 0x031E; // UNIX, version 3.0
        extFileAttr |= generateUnixExternalFileAttr(file.unixPermissions, dir);
    } else { // DOS or other, fallback to DOS
        versionMadeBy = 0x0014; // DOS, version 2.0
        extFileAttr |= generateDosExternalFileAttr(file.dosPermissions, dir);
    }

    // date
    // @see http://www.delorie.com/djgpp/doc/rbinter/it/52/13.html
    // @see http://www.delorie.com/djgpp/doc/rbinter/it/65/16.html
    // @see http://www.delorie.com/djgpp/doc/rbinter/it/66/16.html

    dosTime = date.getUTCHours();
    dosTime = dosTime << 6;
    dosTime = dosTime | date.getUTCMinutes();
    dosTime = dosTime << 5;
    dosTime = dosTime | date.getUTCSeconds() / 2;

    dosDate = date.getUTCFullYear() - 1980;
    dosDate = dosDate << 4;
    dosDate = dosDate | (date.getUTCMonth() + 1);
    dosDate = dosDate << 5;
    dosDate = dosDate | date.getUTCDate();

    if (useUTF8ForFileName) {
        // set the unicode path extra field. unzip needs at least one extra
        // field to correctly handle unicode path, so using the path is as good
        // as any other information. This could improve the situation with
        // other archive managers too.
        // This field is usually used without the utf8 flag, with a non
        // unicode path in the header (winrar, winzip). This helps (a bit)
        // with the messy Windows' default compressed folders feature but
        // breaks on p7zip which doesn't seek the unicode path extra field.
        // So for now, UTF-8 everywhere !
        unicodePathExtraField =
            // Version
            decToHex(1, 1) +
            // NameCRC32
            decToHex(crc32(encodedFileName), 4) +
            // UnicodeName
            utfEncodedFileName;

        extraFields +=
            // Info-ZIP Unicode Path Extra Field
            "\x75\x70" +
            // size
            decToHex(unicodePathExtraField.length, 2) +
            // content
            unicodePathExtraField;
    }

    if(useUTF8ForComment) {

        unicodeCommentExtraField =
            // Version
            decToHex(1, 1) +
            // CommentCRC32
            decToHex(crc32(encodedComment), 4) +
            // UnicodeName
            utfEncodedComment;

        extraFields +=
            // Info-ZIP Unicode Path Extra Field
            "\x75\x63" +
            // size
            decToHex(unicodeCommentExtraField.length, 2) +
            // content
            unicodeCommentExtraField;
    }

    var header = "";

    // version needed to extract
    header += "\x0A\x00";
    // general purpose bit flag
    header += decToHex(bitflag, 2);
    // compression method
    header += compression.magic;
    // last mod file time
    header += decToHex(dosTime, 2);
    // last mod file date
    header += decToHex(dosDate, 2);
    // crc-32
    header += decToHex(dataInfo.crc32, 4);
    // compressed size
    header += decToHex(dataInfo.compressedSize, 4);
    // uncompressed size
    header += decToHex(dataInfo.uncompressedSize, 4);
    // file name length
    header += decToHex(encodedFileName.length, 2);
    // extra field length
    header += decToHex(extraFields.length, 2);


    var fileRecord = signature.LOCAL_FILE_HEADER + header + encodedFileName + extraFields;

    var dirRecord = signature.CENTRAL_FILE_HEADER +
        // version made by (00: DOS)
        decToHex(versionMadeBy, 2) +
        // file header (common to file and central directory)
        header +
        // file comment length
        decToHex(encodedComment.length, 2) +
        // disk number start
        "\x00\x00" +
        // internal file attributes TODO
        "\x00\x00" +
        // external file attributes
        decToHex(extFileAttr, 4) +
        // relative offset of local header
        decToHex(offset, 4) +
        // file name
        encodedFileName +
        // extra field
        extraFields +
        // file comment
        encodedComment;

    return {
        fileRecord: fileRecord,
        dirRecord: dirRecord
    };
};

/**
 * Generate the EOCD record.
 * @param {Number} entriesCount the number of entries in the zip file.
 * @param {Number} centralDirLength the length (in bytes) of the central dir.
 * @param {Number} localDirLength the length (in bytes) of the local dir.
 * @param {String} comment the zip file comment as a binary string.
 * @param {Function} encodeFileName the function to encode the comment.
 * @return {String} the EOCD record.
 */
var generateCentralDirectoryEnd = function (entriesCount, centralDirLength, localDirLength, comment, encodeFileName) {
    var dirEnd = "";
    var encodedComment = utils.transformTo("string", encodeFileName(comment));

    // end of central dir signature
    dirEnd = signature.CENTRAL_DIRECTORY_END +
        // number of this disk
        "\x00\x00" +
        // number of the disk with the start of the central directory
        "\x00\x00" +
        // total number of entries in the central directory on this disk
        decToHex(entriesCount, 2) +
        // total number of entries in the central directory
        decToHex(entriesCount, 2) +
        // size of the central directory   4 bytes
        decToHex(centralDirLength, 4) +
        // offset of start of central directory with respect to the starting disk number
        decToHex(localDirLength, 4) +
        // .ZIP file comment length
        decToHex(encodedComment.length, 2) +
        // .ZIP file comment
        encodedComment;

    return dirEnd;
};

/**
 * Generate data descriptors for a file entry.
 * @param {Object} streamInfo the hash generated by a worker, containing information
 * on the file entry.
 * @return {String} the data descriptors.
 */
var generateDataDescriptors = function (streamInfo) {
    var descriptor = "";
    descriptor = signature.DATA_DESCRIPTOR +
        // crc-32                          4 bytes
        decToHex(streamInfo['crc32'], 4) +
        // compressed size                 4 bytes
        decToHex(streamInfo['compressedSize'], 4) +
        // uncompressed size               4 bytes
        decToHex(streamInfo['uncompressedSize'], 4);

    return descriptor;
};


/**
 * A worker to concatenate other workers to create a zip file.
 * @param {Boolean} streamFiles `true` to stream the content of the files,
 * `false` to accumulate it.
 * @param {String} comment the comment to use.
 * @param {String} platform the platform to use, "UNIX" or "DOS".
 * @param {Function} encodeFileName the function to encode file names and comments.
 */
function ZipFileWorker(streamFiles, comment, platform, encodeFileName) {
    GenericWorker.call(this, "ZipFileWorker");
    // The number of bytes written so far. This doesn't count accumulated chunks.
    this.bytesWritten = 0;
    // The comment of the zip file
    this.zipComment = comment;
    // The platform "generating" the zip file.
    this.zipPlatform = platform;
    // the function to encode file names and comments.
    this.encodeFileName = encodeFileName;
    // Should we stream the content of the files ?
    this.streamFiles = streamFiles;
    // If `streamFiles` is false, we will need to accumulate the content of the
    // files to calculate sizes / crc32 (and write them *before* the content).
    // This boolean indicates if we are accumulating chunks (it will change a lot
    // during the lifetime of this worker).
    this.accumulate = false;
    // The buffer receiving chunks when accumulating content.
    this.contentBuffer = [];
    // The list of generated directory records.
    this.dirRecords = [];
    // The offset (in bytes) from the beginning of the zip file for the current source.
    this.currentSourceOffset = 0;
    // The total number of entries in this zip file.
    this.entriesCount = 0;
    // the name of the file currently being added, null when handling the end of the zip file.
    // Used for the emitted metadata.
    this.currentFile = null;



    this._sources = [];
}
utils.inherits(ZipFileWorker, GenericWorker);

/**
 * @see GenericWorker.push
 */
ZipFileWorker.prototype.push = function (chunk) {

    var currentFilePercent = chunk.meta.percent || 0;
    var entriesCount = this.entriesCount;
    var remainingFiles = this._sources.length;

    if(this.accumulate) {
        this.contentBuffer.push(chunk);
    } else {
        this.bytesWritten += chunk.data.length;

        GenericWorker.prototype.push.call(this, {
            data : chunk.data,
            meta : {
                currentFile : this.currentFile,
                percent : entriesCount ? (currentFilePercent + 100 * (entriesCount - remainingFiles - 1)) / entriesCount : 100
            }
        });
    }
};

/**
 * The worker started a new source (an other worker).
 * @param {Object} streamInfo the streamInfo object from the new source.
 */
ZipFileWorker.prototype.openedSource = function (streamInfo) {
    this.currentSourceOffset = this.bytesWritten;
    this.currentFile = streamInfo['file'].name;

    var streamedContent = this.streamFiles && !streamInfo['file'].dir;

    // don't stream folders (because they don't have any content)
    if(streamedContent) {
        var record = generateZipParts(streamInfo, streamedContent, false, this.currentSourceOffset, this.zipPlatform, this.encodeFileName);
        this.push({
            data : record.fileRecord,
            meta : {percent:0}
        });
    } else {
        // we need to wait for the whole file before pushing anything
        this.accumulate = true;
    }
};

/**
 * The worker finished a source (an other worker).
 * @param {Object} streamInfo the streamInfo object from the finished source.
 */
ZipFileWorker.prototype.closedSource = function (streamInfo) {
    this.accumulate = false;
    var streamedContent = this.streamFiles && !streamInfo['file'].dir;
    var record = generateZipParts(streamInfo, streamedContent, true, this.currentSourceOffset, this.zipPlatform, this.encodeFileName);

    this.dirRecords.push(record.dirRecord);
    if(streamedContent) {
        // after the streamed file, we put data descriptors
        this.push({
            data : generateDataDescriptors(streamInfo),
            meta : {percent:100}
        });
    } else {
        // the content wasn't streamed, we need to push everything now
        // first the file record, then the content
        this.push({
            data : record.fileRecord,
            meta : {percent:0}
        });
        while(this.contentBuffer.length) {
            this.push(this.contentBuffer.shift());
        }
    }
    this.currentFile = null;
};

/**
 * @see GenericWorker.flush
 */
ZipFileWorker.prototype.flush = function () {

    var localDirLength = this.bytesWritten;
    for(var i = 0; i < this.dirRecords.length; i++) {
        this.push({
            data : this.dirRecords[i],
            meta : {percent:100}
        });
    }
    var centralDirLength = this.bytesWritten - localDirLength;

    var dirEnd = generateCentralDirectoryEnd(this.dirRecords.length, centralDirLength, localDirLength, this.zipComment, this.encodeFileName);

    this.push({
        data : dirEnd,
        meta : {percent:100}
    });
};

/**
 * Prepare the next source to be read.
 */
ZipFileWorker.prototype.prepareNextSource = function () {
    this.previous = this._sources.shift();
    this.openedSource(this.previous.streamInfo);
    if (this.isPaused) {
        this.previous.pause();
    } else {
        this.previous.resume();
    }
};

/**
 * @see GenericWorker.registerPrevious
 */
ZipFileWorker.prototype.registerPrevious = function (previous) {
    this._sources.push(previous);
    var self = this;

    previous.on('data', function (chunk) {
        self.processChunk(chunk);
    });
    previous.on('end', function () {
        self.closedSource(self.previous.streamInfo);
        if(self._sources.length) {
            self.prepareNextSource();
        } else {
            self.end();
        }
    });
    previous.on('error', function (e) {
        self.error(e);
    });
    return this;
};

/**
 * @see GenericWorker.resume
 */
ZipFileWorker.prototype.resume = function () {
    if(!GenericWorker.prototype.resume.call(this)) {
        return false;
    }

    if (!this.previous && this._sources.length) {
        this.prepareNextSource();
        return true;
    }
    if (!this.previous && !this._sources.length && !this.generatedError) {
        this.end();
        return true;
    }
};

/**
 * @see GenericWorker.error
 */
ZipFileWorker.prototype.error = function (e) {
    var sources = this._sources;
    if(!GenericWorker.prototype.error.call(this, e)) {
        return false;
    }
    for(var i = 0; i < sources.length; i++) {
        try {
            sources[i].error(e);
        } catch(e) {
            // the `error` exploded, nothing to do
        }
    }
    return true;
};

/**
 * @see GenericWorker.lock
 */
ZipFileWorker.prototype.lock = function () {
    GenericWorker.prototype.lock.call(this);
    var sources = this._sources;
    for(var i = 0; i < sources.length; i++) {
        sources[i].lock();
    }
};

module.exports = ZipFileWorker;

},{"../crc32":4,"../signature":23,"../stream/GenericWorker":28,"../utf8":31,"../utils":32}],9:[function(require,module,exports){
'use strict';

var compressions = require('../compressions');
var ZipFileWorker = require('./ZipFileWorker');

/**
 * Find the compression to use.
 * @param {String} fileCompression the compression defined at the file level, if any.
 * @param {String} zipCompression the compression defined at the load() level.
 * @return {Object} the compression object to use.
 */
var getCompression = function (fileCompression, zipCompression) {

    var compressionName = fileCompression || zipCompression;
    var compression = compressions[compressionName];
    if (!compression) {
        throw new Error(compressionName + " is not a valid compression method !");
    }
    return compression;
};

/**
 * Create a worker to generate a zip file.
 * @param {JSZip} zip the JSZip instance at the right root level.
 * @param {Object} options to generate the zip file.
 * @param {String} comment the comment to use.
 */
exports.generateWorker = function (zip, options, comment) {

    var zipFileWorker = new ZipFileWorker(options.streamFiles, comment, options.platform, options.encodeFileName);
    var entriesCount = 0;
    try {

        zip.forEach(function (relativePath, file) {
            entriesCount++;
            var compression = getCompression(file.options.compression, options.compression);
            var compressionOptions = file.options.compressionOptions || options.compressionOptions || {};
            var dir = file.dir, date = file.date;

            file._compressWorker(compression, compressionOptions)
            .withStreamInfo("file", {
                name : relativePath,
                dir : dir,
                date : date,
                comment : file.comment || "",
                unixPermissions : file.unixPermissions,
                dosPermissions : file.dosPermissions
            })
            .pipe(zipFileWorker);
        });
        zipFileWorker.entriesCount = entriesCount;
    } catch (e) {
        zipFileWorker.error(e);
    }

    return zipFileWorker;
};

},{"../compressions":3,"./ZipFileWorker":8}],10:[function(require,module,exports){
'use strict';

/**
 * Representation a of zip file in js
 * @constructor
 */
function JSZip() {
    // if this constructor is used without `new`, it adds `new` before itself:
    if(!(this instanceof JSZip)) {
        return new JSZip();
    }

    if(arguments.length) {
        throw new Error("The constructor with parameters has been removed in JSZip 3.0, please check the upgrade guide.");
    }

    // object containing the files :
    // {
    //   "folder/" : {...},
    //   "folder/data.txt" : {...}
    // }
    // NOTE: we use a null prototype because we do not
    // want filenames like "toString" coming from a zip file
    // to overwrite methods and attributes in a normal Object.
    this.files = Object.create(null);

    this.comment = null;

    // Where we are in the hierarchy
    this.root = "";
    this.clone = function() {
        var newObj = new JSZip();
        for (var i in this) {
            if (typeof this[i] !== "function") {
                newObj[i] = this[i];
            }
        }
        return newObj;
    };
}
JSZip.prototype = require('./object');
JSZip.prototype.loadAsync = require('./load');
JSZip.support = require('./support');
JSZip.defaults = require('./defaults');

// TODO find a better way to handle this version,
// a require('package.json').version doesn't work with webpack, see #327
JSZip.version = "3.10.0";

JSZip.loadAsync = function (content, options) {
    return new JSZip().loadAsync(content, options);
};

JSZip.external = require("./external");
module.exports = JSZip;

},{"./defaults":5,"./external":6,"./load":11,"./object":15,"./support":30}],11:[function(require,module,exports){
'use strict';
var utils = require('./utils');
var external = require("./external");
var utf8 = require('./utf8');
var ZipEntries = require('./zipEntries');
var Crc32Probe = require('./stream/Crc32Probe');
var nodejsUtils = require("./nodejsUtils");

/**
 * Check the CRC32 of an entry.
 * @param {ZipEntry} zipEntry the zip entry to check.
 * @return {Promise} the result.
 */
function checkEntryCRC32(zipEntry) {
    return new external.Promise(function (resolve, reject) {
        var worker = zipEntry.decompressed.getContentWorker().pipe(new Crc32Probe());
        worker.on("error", function (e) {
            reject(e);
        })
            .on("end", function () {
                if (worker.streamInfo.crc32 !== zipEntry.decompressed.crc32) {
                    reject(new Error("Corrupted zip : CRC32 mismatch"));
                } else {
                    resolve();
                }
            })
            .resume();
    });
}

module.exports = function (data, options) {
    var zip = this;
    options = utils.extend(options || {}, {
        base64: false,
        checkCRC32: false,
        optimizedBinaryString: false,
        createFolders: false,
        decodeFileName: utf8.utf8decode
    });

    if (nodejsUtils.isNode && nodejsUtils.isStream(data)) {
        return external.Promise.reject(new Error("JSZip can't accept a stream when loading a zip file."));
    }

    return utils.prepareContent("the loaded zip file", data, true, options.optimizedBinaryString, options.base64)
        .then(function (data) {
            var zipEntries = new ZipEntries(options);
            zipEntries.load(data);
            return zipEntries;
        }).then(function checkCRC32(zipEntries) {
            var promises = [external.Promise.resolve(zipEntries)];
            var files = zipEntries.files;
            if (options.checkCRC32) {
                for (var i = 0; i < files.length; i++) {
                    promises.push(checkEntryCRC32(files[i]));
                }
            }
            return external.Promise.all(promises);
        }).then(function addFiles(results) {
            var zipEntries = results.shift();
            var files = zipEntries.files;
            for (var i = 0; i < files.length; i++) {
                var input = files[i];

                var unsafeName = input.fileNameStr;
                var safeName = utils.resolve(input.fileNameStr);

                zip.file(safeName, input.decompressed, {
                    binary: true,
                    optimizedBinaryString: true,
                    date: input.date,
                    dir: input.dir,
                    comment: input.fileCommentStr.length ? input.fileCommentStr : null,
                    unixPermissions: input.unixPermissions,
                    dosPermissions: input.dosPermissions,
                    createFolders: options.createFolders
                });
                if (!input.dir) {
                    zip.file(safeName).unsafeOriginalName = unsafeName;
                }
            }
            if (zipEntries.zipComment.length) {
                zip.comment = zipEntries.zipComment;
            }

            return zip;
        });
};

},{"./external":6,"./nodejsUtils":14,"./stream/Crc32Probe":25,"./utf8":31,"./utils":32,"./zipEntries":33}],12:[function(require,module,exports){
"use strict";

var utils = require('../utils');
var GenericWorker = require('../stream/GenericWorker');

/**
 * A worker that use a nodejs stream as source.
 * @constructor
 * @param {String} filename the name of the file entry for this stream.
 * @param {Readable} stream the nodejs stream.
 */
function NodejsStreamInputAdapter(filename, stream) {
    GenericWorker.call(this, "Nodejs stream input adapter for " + filename);
    this._upstreamEnded = false;
    this._bindStream(stream);
}

utils.inherits(NodejsStreamInputAdapter, GenericWorker);

/**
 * Prepare the stream and bind the callbacks on it.
 * Do this ASAP on node 0.10 ! A lazy binding doesn't always work.
 * @param {Stream} stream the nodejs stream to use.
 */
NodejsStreamInputAdapter.prototype._bindStream = function (stream) {
    var self = this;
    this._stream = stream;
    stream.pause();
    stream
    .on("data", function (chunk) {
        self.push({
            data: chunk,
            meta : {
                percent : 0
            }
        });
    })
    .on("error", function (e) {
        if(self.isPaused) {
            this.generatedError = e;
        } else {
            self.error(e);
        }
    })
    .on("end", function () {
        if(self.isPaused) {
            self._upstreamEnded = true;
        } else {
            self.end();
        }
    });
};
NodejsStreamInputAdapter.prototype.pause = function () {
    if(!GenericWorker.prototype.pause.call(this)) {
        return false;
    }
    this._stream.pause();
    return true;
};
NodejsStreamInputAdapter.prototype.resume = function () {
    if(!GenericWorker.prototype.resume.call(this)) {
        return false;
    }

    if(this._upstreamEnded) {
        this.end();
    } else {
        this._stream.resume();
    }

    return true;
};

module.exports = NodejsStreamInputAdapter;

},{"../stream/GenericWorker":28,"../utils":32}],13:[function(require,module,exports){
'use strict';

var Readable = require('readable-stream').Readable;

var utils = require('../utils');
utils.inherits(NodejsStreamOutputAdapter, Readable);

/**
* A nodejs stream using a worker as source.
* @see the SourceWrapper in http://nodejs.org/api/stream.html
* @constructor
* @param {StreamHelper} helper the helper wrapping the worker
* @param {Object} options the nodejs stream options
* @param {Function} updateCb the update callback.
*/
function NodejsStreamOutputAdapter(helper, options, updateCb) {
    Readable.call(this, options);
    this._helper = helper;

    var self = this;
    helper.on("data", function (data, meta) {
        if (!self.push(data)) {
            self._helper.pause();
        }
        if(updateCb) {
            updateCb(meta);
        }
    })
    .on("error", function(e) {
        self.emit('error', e);
    })
    .on("end", function () {
        self.push(null);
    });
}


NodejsStreamOutputAdapter.prototype._read = function() {
    this._helper.resume();
};

module.exports = NodejsStreamOutputAdapter;

},{"../utils":32,"readable-stream":16}],14:[function(require,module,exports){
'use strict';

module.exports = {
    /**
     * True if this is running in Nodejs, will be undefined in a browser.
     * In a browser, browserify won't include this file and the whole module
     * will be resolved an empty object.
     */
    isNode : typeof Buffer !== "undefined",
    /**
     * Create a new nodejs Buffer from an existing content.
     * @param {Object} data the data to pass to the constructor.
     * @param {String} encoding the encoding to use.
     * @return {Buffer} a new Buffer.
     */
    newBufferFrom: function(data, encoding) {
        if (Buffer.from && Buffer.from !== Uint8Array.from) {
            return Buffer.from(data, encoding);
        } else {
            if (typeof data === "number") {
                // Safeguard for old Node.js versions. On newer versions,
                // Buffer.from(number) / Buffer(number, encoding) already throw.
                throw new Error("The \"data\" argument must not be a number");
            }
            return new Buffer(data, encoding);
        }
    },
    /**
     * Create a new nodejs Buffer with the specified size.
     * @param {Integer} size the size of the buffer.
     * @return {Buffer} a new Buffer.
     */
    allocBuffer: function (size) {
        if (Buffer.alloc) {
            return Buffer.alloc(size);
        } else {
            var buf = new Buffer(size);
            buf.fill(0);
            return buf;
        }
    },
    /**
     * Find out if an object is a Buffer.
     * @param {Object} b the object to test.
     * @return {Boolean} true if the object is a Buffer, false otherwise.
     */
    isBuffer : function(b){
        return Buffer.isBuffer(b);
    },

    isStream : function (obj) {
        return obj &&
            typeof obj.on === "function" &&
            typeof obj.pause === "function" &&
            typeof obj.resume === "function";
    }
};

},{}],15:[function(require,module,exports){
'use strict';
var utf8 = require('./utf8');
var utils = require('./utils');
var GenericWorker = require('./stream/GenericWorker');
var StreamHelper = require('./stream/StreamHelper');
var defaults = require('./defaults');
var CompressedObject = require('./compressedObject');
var ZipObject = require('./zipObject');
var generate = require("./generate");
var nodejsUtils = require("./nodejsUtils");
var NodejsStreamInputAdapter = require("./nodejs/NodejsStreamInputAdapter");


/**
 * Add a file in the current folder.
 * @private
 * @param {string} name the name of the file
 * @param {String|ArrayBuffer|Uint8Array|Buffer} data the data of the file
 * @param {Object} originalOptions the options of the file
 * @return {Object} the new file.
 */
var fileAdd = function(name, data, originalOptions) {
    // be sure sub folders exist
    var dataType = utils.getTypeOf(data),
        parent;


    /*
     * Correct options.
     */

    var o = utils.extend(originalOptions || {}, defaults);
    o.date = o.date || new Date();
    if (o.compression !== null) {
        o.compression = o.compression.toUpperCase();
    }

    if (typeof o.unixPermissions === "string") {
        o.unixPermissions = parseInt(o.unixPermissions, 8);
    }

    // UNX_IFDIR  0040000 see zipinfo.c
    if (o.unixPermissions && (o.unixPermissions & 0x4000)) {
        o.dir = true;
    }
    // Bit 4    Directory
    if (o.dosPermissions && (o.dosPermissions & 0x0010)) {
        o.dir = true;
    }

    if (o.dir) {
        name = forceTrailingSlash(name);
    }
    if (o.createFolders && (parent = parentFolder(name))) {
        folderAdd.call(this, parent, true);
    }

    var isUnicodeString = dataType === "string" && o.binary === false && o.base64 === false;
    if (!originalOptions || typeof originalOptions.binary === "undefined") {
        o.binary = !isUnicodeString;
    }


    var isCompressedEmpty = (data instanceof CompressedObject) && data.uncompressedSize === 0;

    if (isCompressedEmpty || o.dir || !data || data.length === 0) {
        o.base64 = false;
        o.binary = true;
        data = "";
        o.compression = "STORE";
        dataType = "string";
    }

    /*
     * Convert content to fit.
     */

    var zipObjectContent = null;
    if (data instanceof CompressedObject || data instanceof GenericWorker) {
        zipObjectContent = data;
    } else if (nodejsUtils.isNode && nodejsUtils.isStream(data)) {
        zipObjectContent = new NodejsStreamInputAdapter(name, data);
    } else {
        zipObjectContent = utils.prepareContent(name, data, o.binary, o.optimizedBinaryString, o.base64);
    }

    var object = new ZipObject(name, zipObjectContent, o);
    this.files[name] = object;
    /*
    TODO: we can't throw an exception because we have async promises
    (we can have a promise of a Date() for example) but returning a
    promise is useless because file(name, data) returns the JSZip
    object for chaining. Should we break that to allow the user
    to catch the error ?

    return external.Promise.resolve(zipObjectContent)
    .then(function () {
        return object;
    });
    */
};

/**
 * Find the parent folder of the path.
 * @private
 * @param {string} path the path to use
 * @return {string} the parent folder, or ""
 */
var parentFolder = function (path) {
    if (path.slice(-1) === '/') {
        path = path.substring(0, path.length - 1);
    }
    var lastSlash = path.lastIndexOf('/');
    return (lastSlash > 0) ? path.substring(0, lastSlash) : "";
};

/**
 * Returns the path with a slash at the end.
 * @private
 * @param {String} path the path to check.
 * @return {String} the path with a trailing slash.
 */
var forceTrailingSlash = function(path) {
    // Check the name ends with a /
    if (path.slice(-1) !== "/") {
        path += "/"; // IE doesn't like substr(-1)
    }
    return path;
};

/**
 * Add a (sub) folder in the current folder.
 * @private
 * @param {string} name the folder's name
 * @param {boolean=} [createFolders] If true, automatically create sub
 *  folders. Defaults to false.
 * @return {Object} the new folder.
 */
var folderAdd = function(name, createFolders) {
    createFolders = (typeof createFolders !== 'undefined') ? createFolders : defaults.createFolders;

    name = forceTrailingSlash(name);

    // Does this folder already exist?
    if (!this.files[name]) {
        fileAdd.call(this, name, null, {
            dir: true,
            createFolders: createFolders
        });
    }
    return this.files[name];
};

/**
* Cross-window, cross-Node-context regular expression detection
* @param  {Object}  object Anything
* @return {Boolean}        true if the object is a regular expression,
* false otherwise
*/
function isRegExp(object) {
    return Object.prototype.toString.call(object) === "[object RegExp]";
}

// return the actual prototype of JSZip
var out = {
    /**
     * @see loadAsync
     */
    load: function() {
        throw new Error("This method has been removed in JSZip 3.0, please check the upgrade guide.");
    },


    /**
     * Call a callback function for each entry at this folder level.
     * @param {Function} cb the callback function:
     * function (relativePath, file) {...}
     * It takes 2 arguments : the relative path and the file.
     */
    forEach: function(cb) {
        var filename, relativePath, file;
        /* jshint ignore:start */
        // ignore warning about unwanted properties because this.files is a null prototype object
        for (filename in this.files) {
            file = this.files[filename];
            relativePath = filename.slice(this.root.length, filename.length);
            if (relativePath && filename.slice(0, this.root.length) === this.root) { // the file is in the current root
                cb(relativePath, file); // TODO reverse the parameters ? need to be clean AND consistent with the filter search fn...
            }
        }
        /* jshint ignore:end */
    },

    /**
     * Filter nested files/folders with the specified function.
     * @param {Function} search the predicate to use :
     * function (relativePath, file) {...}
     * It takes 2 arguments : the relative path and the file.
     * @return {Array} An array of matching elements.
     */
    filter: function(search) {
        var result = [];
        this.forEach(function (relativePath, entry) {
            if (search(relativePath, entry)) { // the file matches the function
                result.push(entry);
            }

        });
        return result;
    },

    /**
     * Add a file to the zip file, or search a file.
     * @param   {string|RegExp} name The name of the file to add (if data is defined),
     * the name of the file to find (if no data) or a regex to match files.
     * @param   {String|ArrayBuffer|Uint8Array|Buffer} data  The file data, either raw or base64 encoded
     * @param   {Object} o     File options
     * @return  {JSZip|Object|Array} this JSZip object (when adding a file),
     * a file (when searching by string) or an array of files (when searching by regex).
     */
    file: function(name, data, o) {
        if (arguments.length === 1) {
            if (isRegExp(name)) {
                var regexp = name;
                return this.filter(function(relativePath, file) {
                    return !file.dir && regexp.test(relativePath);
                });
            }
            else { // text
                var obj = this.files[this.root + name];
                if (obj && !obj.dir) {
                    return obj;
                } else {
                    return null;
                }
            }
        }
        else { // more than one argument : we have data !
            name = this.root + name;
            fileAdd.call(this, name, data, o);
        }
        return this;
    },

    /**
     * Add a directory to the zip file, or search.
     * @param   {String|RegExp} arg The name of the directory to add, or a regex to search folders.
     * @return  {JSZip} an object with the new directory as the root, or an array containing matching folders.
     */
    folder: function(arg) {
        if (!arg) {
            return this;
        }

        if (isRegExp(arg)) {
            return this.filter(function(relativePath, file) {
                return file.dir && arg.test(relativePath);
            });
        }

        // else, name is a new folder
        var name = this.root + arg;
        var newFolder = folderAdd.call(this, name);

        // Allow chaining by returning a new object with this folder as the root
        var ret = this.clone();
        ret.root = newFolder.name;
        return ret;
    },

    /**
     * Delete a file, or a directory and all sub-files, from the zip
     * @param {string} name the name of the file to delete
     * @return {JSZip} this JSZip object
     */
    remove: function(name) {
        name = this.root + name;
        var file = this.files[name];
        if (!file) {
            // Look for any folders
            if (name.slice(-1) !== "/") {
                name += "/";
            }
            file = this.files[name];
        }

        if (file && !file.dir) {
            // file
            delete this.files[name];
        } else {
            // maybe a folder, delete recursively
            var kids = this.filter(function(relativePath, file) {
                return file.name.slice(0, name.length) === name;
            });
            for (var i = 0; i < kids.length; i++) {
                delete this.files[kids[i].name];
            }
        }

        return this;
    },

    /**
     * Generate the complete zip file
     * @param {Object} options the options to generate the zip file :
     * - compression, "STORE" by default.
     * - type, "base64" by default. Values are : string, base64, uint8array, arraybuffer, blob.
     * @return {String|Uint8Array|ArrayBuffer|Buffer|Blob} the zip file
     */
    generate: function(options) {
        throw new Error("This method has been removed in JSZip 3.0, please check the upgrade guide.");
    },

    /**
     * Generate the complete zip file as an internal stream.
     * @param {Object} options the options to generate the zip file :
     * - compression, "STORE" by default.
     * - type, "base64" by default. Values are : string, base64, uint8array, arraybuffer, blob.
     * @return {StreamHelper} the streamed zip file.
     */
    generateInternalStream: function(options) {
      var worker, opts = {};
      try {
          opts = utils.extend(options || {}, {
              streamFiles: false,
              compression: "STORE",
              compressionOptions : null,
              type: "",
              platform: "DOS",
              comment: null,
              mimeType: 'application/zip',
              encodeFileName: utf8.utf8encode
          });

          opts.type = opts.type.toLowerCase();
          opts.compression = opts.compression.toUpperCase();

          // "binarystring" is preferred but the internals use "string".
          if(opts.type === "binarystring") {
            opts.type = "string";
          }

          if (!opts.type) {
            throw new Error("No output type specified.");
          }

          utils.checkSupport(opts.type);

          // accept nodejs `process.platform`
          if(
              opts.platform === 'darwin' ||
              opts.platform === 'freebsd' ||
              opts.platform === 'linux' ||
              opts.platform === 'sunos'
          ) {
              opts.platform = "UNIX";
          }
          if (opts.platform === 'win32') {
              opts.platform = "DOS";
          }

          var comment = opts.comment || this.comment || "";
          worker = generate.generateWorker(this, opts, comment);
      } catch (e) {
        worker = new GenericWorker("error");
        worker.error(e);
      }
      return new StreamHelper(worker, opts.type || "string", opts.mimeType);
    },
    /**
     * Generate the complete zip file asynchronously.
     * @see generateInternalStream
     */
    generateAsync: function(options, onUpdate) {
        return this.generateInternalStream(options).accumulate(onUpdate);
    },
    /**
     * Generate the complete zip file asynchronously.
     * @see generateInternalStream
     */
    generateNodeStream: function(options, onUpdate) {
        options = options || {};
        if (!options.type) {
            options.type = "nodebuffer";
        }
        return this.generateInternalStream(options).toNodejsStream(onUpdate);
    }
};
module.exports = out;

},{"./compressedObject":2,"./defaults":5,"./generate":9,"./nodejs/NodejsStreamInputAdapter":12,"./nodejsUtils":14,"./stream/GenericWorker":28,"./stream/StreamHelper":29,"./utf8":31,"./utils":32,"./zipObject":35}],16:[function(require,module,exports){
/*
 * This file is used by module bundlers (browserify/webpack/etc) when
 * including a stream implementation. We use "readable-stream" to get a
 * consistent behavior between nodejs versions but bundlers often have a shim
 * for "stream". Using this shim greatly improve the compatibility and greatly
 * reduce the final size of the bundle (only one stream implementation, not
 * two).
 */
module.exports = require("stream");

},{"stream":undefined}],17:[function(require,module,exports){
'use strict';
var DataReader = require('./DataReader');
var utils = require('../utils');

function ArrayReader(data) {
    DataReader.call(this, data);
	for(var i = 0; i < this.data.length; i++) {
		data[i] = data[i] & 0xFF;
	}
}
utils.inherits(ArrayReader, DataReader);
/**
 * @see DataReader.byteAt
 */
ArrayReader.prototype.byteAt = function(i) {
    return this.data[this.zero + i];
};
/**
 * @see DataReader.lastIndexOfSignature
 */
ArrayReader.prototype.lastIndexOfSignature = function(sig) {
    var sig0 = sig.charCodeAt(0),
        sig1 = sig.charCodeAt(1),
        sig2 = sig.charCodeAt(2),
        sig3 = sig.charCodeAt(3);
    for (var i = this.length - 4; i >= 0; --i) {
        if (this.data[i] === sig0 && this.data[i + 1] === sig1 && this.data[i + 2] === sig2 && this.data[i + 3] === sig3) {
            return i - this.zero;
        }
    }

    return -1;
};
/**
 * @see DataReader.readAndCheckSignature
 */
ArrayReader.prototype.readAndCheckSignature = function (sig) {
    var sig0 = sig.charCodeAt(0),
        sig1 = sig.charCodeAt(1),
        sig2 = sig.charCodeAt(2),
        sig3 = sig.charCodeAt(3),
        data = this.readData(4);
    return sig0 === data[0] && sig1 === data[1] && sig2 === data[2] && sig3 === data[3];
};
/**
 * @see DataReader.readData
 */
ArrayReader.prototype.readData = function(size) {
    this.checkOffset(size);
    if(size === 0) {
        return [];
    }
    var result = this.data.slice(this.zero + this.index, this.zero + this.index + size);
    this.index += size;
    return result;
};
module.exports = ArrayReader;

},{"../utils":32,"./DataReader":18}],18:[function(require,module,exports){
'use strict';
var utils = require('../utils');

function DataReader(data) {
    this.data = data; // type : see implementation
    this.length = data.length;
    this.index = 0;
    this.zero = 0;
}
DataReader.prototype = {
    /**
     * Check that the offset will not go too far.
     * @param {string} offset the additional offset to check.
     * @throws {Error} an Error if the offset is out of bounds.
     */
    checkOffset: function(offset) {
        this.checkIndex(this.index + offset);
    },
    /**
     * Check that the specified index will not be too far.
     * @param {string} newIndex the index to check.
     * @throws {Error} an Error if the index is out of bounds.
     */
    checkIndex: function(newIndex) {
        if (this.length < this.zero + newIndex || newIndex < 0) {
            throw new Error("End of data reached (data length = " + this.length + ", asked index = " + (newIndex) + "). Corrupted zip ?");
        }
    },
    /**
     * Change the index.
     * @param {number} newIndex The new index.
     * @throws {Error} if the new index is out of the data.
     */
    setIndex: function(newIndex) {
        this.checkIndex(newIndex);
        this.index = newIndex;
    },
    /**
     * Skip the next n bytes.
     * @param {number} n the number of bytes to skip.
     * @throws {Error} if the new index is out of the data.
     */
    skip: function(n) {
        this.setIndex(this.index + n);
    },
    /**
     * Get the byte at the specified index.
     * @param {number} i the index to use.
     * @return {number} a byte.
     */
    byteAt: function(i) {
        // see implementations
    },
    /**
     * Get the next number with a given byte size.
     * @param {number} size the number of bytes to read.
     * @return {number} the corresponding number.
     */
    readInt: function(size) {
        var result = 0,
            i;
        this.checkOffset(size);
        for (i = this.index + size - 1; i >= this.index; i--) {
            result = (result << 8) + this.byteAt(i);
        }
        this.index += size;
        return result;
    },
    /**
     * Get the next string with a given byte size.
     * @param {number} size the number of bytes to read.
     * @return {string} the corresponding string.
     */
    readString: function(size) {
        return utils.transformTo("string", this.readData(size));
    },
    /**
     * Get raw data without conversion, <size> bytes.
     * @param {number} size the number of bytes to read.
     * @return {Object} the raw data, implementation specific.
     */
    readData: function(size) {
        // see implementations
    },
    /**
     * Find the last occurrence of a zip signature (4 bytes).
     * @param {string} sig the signature to find.
     * @return {number} the index of the last occurrence, -1 if not found.
     */
    lastIndexOfSignature: function(sig) {
        // see implementations
    },
    /**
     * Read the signature (4 bytes) at the current position and compare it with sig.
     * @param {string} sig the expected signature
     * @return {boolean} true if the signature matches, false otherwise.
     */
    readAndCheckSignature: function(sig) {
        // see implementations
    },
    /**
     * Get the next date.
     * @return {Date} the date.
     */
    readDate: function() {
        var dostime = this.readInt(4);
        return new Date(Date.UTC(
        ((dostime >> 25) & 0x7f) + 1980, // year
        ((dostime >> 21) & 0x0f) - 1, // month
        (dostime >> 16) & 0x1f, // day
        (dostime >> 11) & 0x1f, // hour
        (dostime >> 5) & 0x3f, // minute
        (dostime & 0x1f) << 1)); // second
    }
};
module.exports = DataReader;

},{"../utils":32}],19:[function(require,module,exports){
'use strict';
var Uint8ArrayReader = require('./Uint8ArrayReader');
var utils = require('../utils');

function NodeBufferReader(data) {
    Uint8ArrayReader.call(this, data);
}
utils.inherits(NodeBufferReader, Uint8ArrayReader);

/**
 * @see DataReader.readData
 */
NodeBufferReader.prototype.readData = function(size) {
    this.checkOffset(size);
    var result = this.data.slice(this.zero + this.index, this.zero + this.index + size);
    this.index += size;
    return result;
};
module.exports = NodeBufferReader;

},{"../utils":32,"./Uint8ArrayReader":21}],20:[function(require,module,exports){
'use strict';
var DataReader = require('./DataReader');
var utils = require('../utils');

function StringReader(data) {
    DataReader.call(this, data);
}
utils.inherits(StringReader, DataReader);
/**
 * @see DataReader.byteAt
 */
StringReader.prototype.byteAt = function(i) {
    return this.data.charCodeAt(this.zero + i);
};
/**
 * @see DataReader.lastIndexOfSignature
 */
StringReader.prototype.lastIndexOfSignature = function(sig) {
    return this.data.lastIndexOf(sig) - this.zero;
};
/**
 * @see DataReader.readAndCheckSignature
 */
StringReader.prototype.readAndCheckSignature = function (sig) {
    var data = this.readData(4);
    return sig === data;
};
/**
 * @see DataReader.readData
 */
StringReader.prototype.readData = function(size) {
    this.checkOffset(size);
    // this will work because the constructor applied the "& 0xff" mask.
    var result = this.data.slice(this.zero + this.index, this.zero + this.index + size);
    this.index += size;
    return result;
};
module.exports = StringReader;

},{"../utils":32,"./DataReader":18}],21:[function(require,module,exports){
'use strict';
var ArrayReader = require('./ArrayReader');
var utils = require('../utils');

function Uint8ArrayReader(data) {
    ArrayReader.call(this, data);
}
utils.inherits(Uint8ArrayReader, ArrayReader);
/**
 * @see DataReader.readData
 */
Uint8ArrayReader.prototype.readData = function(size) {
    this.checkOffset(size);
    if(size === 0) {
        // in IE10, when using subarray(idx, idx), we get the array [0x00] instead of [].
        return new Uint8Array(0);
    }
    var result = this.data.subarray(this.zero + this.index, this.zero + this.index + size);
    this.index += size;
    return result;
};
module.exports = Uint8ArrayReader;

},{"../utils":32,"./ArrayReader":17}],22:[function(require,module,exports){
'use strict';

var utils = require('../utils');
var support = require('../support');
var ArrayReader = require('./ArrayReader');
var StringReader = require('./StringReader');
var NodeBufferReader = require('./NodeBufferReader');
var Uint8ArrayReader = require('./Uint8ArrayReader');

/**
 * Create a reader adapted to the data.
 * @param {String|ArrayBuffer|Uint8Array|Buffer} data the data to read.
 * @return {DataReader} the data reader.
 */
module.exports = function (data) {
    var type = utils.getTypeOf(data);
    utils.checkSupport(type);
    if (type === "string" && !support.uint8array) {
        return new StringReader(data);
    }
    if (type === "nodebuffer") {
        return new NodeBufferReader(data);
    }
    if (support.uint8array) {
        return new Uint8ArrayReader(utils.transformTo("uint8array", data));
    }
    return new ArrayReader(utils.transformTo("array", data));
};

},{"../support":30,"../utils":32,"./ArrayReader":17,"./NodeBufferReader":19,"./StringReader":20,"./Uint8ArrayReader":21}],23:[function(require,module,exports){
'use strict';
exports.LOCAL_FILE_HEADER = "PK\x03\x04";
exports.CENTRAL_FILE_HEADER = "PK\x01\x02";
exports.CENTRAL_DIRECTORY_END = "PK\x05\x06";
exports.ZIP64_CENTRAL_DIRECTORY_LOCATOR = "PK\x06\x07";
exports.ZIP64_CENTRAL_DIRECTORY_END = "PK\x06\x06";
exports.DATA_DESCRIPTOR = "PK\x07\x08";

},{}],24:[function(require,module,exports){
'use strict';

var GenericWorker = require('./GenericWorker');
var utils = require('../utils');

/**
 * A worker which convert chunks to a specified type.
 * @constructor
 * @param {String} destType the destination type.
 */
function ConvertWorker(destType) {
    GenericWorker.call(this, "ConvertWorker to " + destType);
    this.destType = destType;
}
utils.inherits(ConvertWorker, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
ConvertWorker.prototype.processChunk = function (chunk) {
    this.push({
        data : utils.transformTo(this.destType, chunk.data),
        meta : chunk.meta
    });
};
module.exports = ConvertWorker;

},{"../utils":32,"./GenericWorker":28}],25:[function(require,module,exports){
'use strict';

var GenericWorker = require('./GenericWorker');
var crc32 = require('../crc32');
var utils = require('../utils');

/**
 * A worker which calculate the crc32 of the data flowing through.
 * @constructor
 */
function Crc32Probe() {
    GenericWorker.call(this, "Crc32Probe");
    this.withStreamInfo("crc32", 0);
}
utils.inherits(Crc32Probe, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
Crc32Probe.prototype.processChunk = function (chunk) {
    this.streamInfo.crc32 = crc32(chunk.data, this.streamInfo.crc32 || 0);
    this.push(chunk);
};
module.exports = Crc32Probe;

},{"../crc32":4,"../utils":32,"./GenericWorker":28}],26:[function(require,module,exports){
'use strict';

var utils = require('../utils');
var GenericWorker = require('./GenericWorker');

/**
 * A worker which calculate the total length of the data flowing through.
 * @constructor
 * @param {String} propName the name used to expose the length
 */
function DataLengthProbe(propName) {
    GenericWorker.call(this, "DataLengthProbe for " + propName);
    this.propName = propName;
    this.withStreamInfo(propName, 0);
}
utils.inherits(DataLengthProbe, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
DataLengthProbe.prototype.processChunk = function (chunk) {
    if(chunk) {
        var length = this.streamInfo[this.propName] || 0;
        this.streamInfo[this.propName] = length + chunk.data.length;
    }
    GenericWorker.prototype.processChunk.call(this, chunk);
};
module.exports = DataLengthProbe;


},{"../utils":32,"./GenericWorker":28}],27:[function(require,module,exports){
'use strict';

var utils = require('../utils');
var GenericWorker = require('./GenericWorker');

// the size of the generated chunks
// TODO expose this as a public variable
var DEFAULT_BLOCK_SIZE = 16 * 1024;

/**
 * A worker that reads a content and emits chunks.
 * @constructor
 * @param {Promise} dataP the promise of the data to split
 */
function DataWorker(dataP) {
    GenericWorker.call(this, "DataWorker");
    var self = this;
    this.dataIsReady = false;
    this.index = 0;
    this.max = 0;
    this.data = null;
    this.type = "";

    this._tickScheduled = false;

    dataP.then(function (data) {
        self.dataIsReady = true;
        self.data = data;
        self.max = data && data.length || 0;
        self.type = utils.getTypeOf(data);
        if(!self.isPaused) {
            self._tickAndRepeat();
        }
    }, function (e) {
        self.error(e);
    });
}

utils.inherits(DataWorker, GenericWorker);

/**
 * @see GenericWorker.cleanUp
 */
DataWorker.prototype.cleanUp = function () {
    GenericWorker.prototype.cleanUp.call(this);
    this.data = null;
};

/**
 * @see GenericWorker.resume
 */
DataWorker.prototype.resume = function () {
    if(!GenericWorker.prototype.resume.call(this)) {
        return false;
    }

    if (!this._tickScheduled && this.dataIsReady) {
        this._tickScheduled = true;
        utils.delay(this._tickAndRepeat, [], this);
    }
    return true;
};

/**
 * Trigger a tick a schedule an other call to this function.
 */
DataWorker.prototype._tickAndRepeat = function() {
    this._tickScheduled = false;
    if(this.isPaused || this.isFinished) {
        return;
    }
    this._tick();
    if(!this.isFinished) {
        utils.delay(this._tickAndRepeat, [], this);
        this._tickScheduled = true;
    }
};

/**
 * Read and push a chunk.
 */
DataWorker.prototype._tick = function() {

    if(this.isPaused || this.isFinished) {
        return false;
    }

    var size = DEFAULT_BLOCK_SIZE;
    var data = null, nextIndex = Math.min(this.max, this.index + size);
    if (this.index >= this.max) {
        // EOF
        return this.end();
    } else {
        switch(this.type) {
            case "string":
                data = this.data.substring(this.index, nextIndex);
            break;
            case "uint8array":
                data = this.data.subarray(this.index, nextIndex);
            break;
            case "array":
            case "nodebuffer":
                data = this.data.slice(this.index, nextIndex);
            break;
        }
        this.index = nextIndex;
        return this.push({
            data : data,
            meta : {
                percent : this.max ? this.index / this.max * 100 : 0
            }
        });
    }
};

module.exports = DataWorker;

},{"../utils":32,"./GenericWorker":28}],28:[function(require,module,exports){
'use strict';

/**
 * A worker that does nothing but passing chunks to the next one. This is like
 * a nodejs stream but with some differences. On the good side :
 * - it works on IE 6-9 without any issue / polyfill
 * - it weights less than the full dependencies bundled with browserify
 * - it forwards errors (no need to declare an error handler EVERYWHERE)
 *
 * A chunk is an object with 2 attributes : `meta` and `data`. The former is an
 * object containing anything (`percent` for example), see each worker for more
 * details. The latter is the real data (String, Uint8Array, etc).
 *
 * @constructor
 * @param {String} name the name of the stream (mainly used for debugging purposes)
 */
function GenericWorker(name) {
    // the name of the worker
    this.name = name || "default";
    // an object containing metadata about the workers chain
    this.streamInfo = {};
    // an error which happened when the worker was paused
    this.generatedError = null;
    // an object containing metadata to be merged by this worker into the general metadata
    this.extraStreamInfo = {};
    // true if the stream is paused (and should not do anything), false otherwise
    this.isPaused = true;
    // true if the stream is finished (and should not do anything), false otherwise
    this.isFinished = false;
    // true if the stream is locked to prevent further structure updates (pipe), false otherwise
    this.isLocked = false;
    // the event listeners
    this._listeners = {
        'data':[],
        'end':[],
        'error':[]
    };
    // the previous worker, if any
    this.previous = null;
}

GenericWorker.prototype = {
    /**
     * Push a chunk to the next workers.
     * @param {Object} chunk the chunk to push
     */
    push : function (chunk) {
        this.emit("data", chunk);
    },
    /**
     * End the stream.
     * @return {Boolean} true if this call ended the worker, false otherwise.
     */
    end : function () {
        if (this.isFinished) {
            return false;
        }

        this.flush();
        try {
            this.emit("end");
            this.cleanUp();
            this.isFinished = true;
        } catch (e) {
            this.emit("error", e);
        }
        return true;
    },
    /**
     * End the stream with an error.
     * @param {Error} e the error which caused the premature end.
     * @return {Boolean} true if this call ended the worker with an error, false otherwise.
     */
    error : function (e) {
        if (this.isFinished) {
            return false;
        }

        if(this.isPaused) {
            this.generatedError = e;
        } else {
            this.isFinished = true;

            this.emit("error", e);

            // in the workers chain exploded in the middle of the chain,
            // the error event will go downward but we also need to notify
            // workers upward that there has been an error.
            if(this.previous) {
                this.previous.error(e);
            }

            this.cleanUp();
        }
        return true;
    },
    /**
     * Add a callback on an event.
     * @param {String} name the name of the event (data, end, error)
     * @param {Function} listener the function to call when the event is triggered
     * @return {GenericWorker} the current object for chainability
     */
    on : function (name, listener) {
        this._listeners[name].push(listener);
        return this;
    },
    /**
     * Clean any references when a worker is ending.
     */
    cleanUp : function () {
        this.streamInfo = this.generatedError = this.extraStreamInfo = null;
        this._listeners = [];
    },
    /**
     * Trigger an event. This will call registered callback with the provided arg.
     * @param {String} name the name of the event (data, end, error)
     * @param {Object} arg the argument to call the callback with.
     */
    emit : function (name, arg) {
        if (this._listeners[name]) {
            for(var i = 0; i < this._listeners[name].length; i++) {
                this._listeners[name][i].call(this, arg);
            }
        }
    },
    /**
     * Chain a worker with an other.
     * @param {Worker} next the worker receiving events from the current one.
     * @return {worker} the next worker for chainability
     */
    pipe : function (next) {
        return next.registerPrevious(this);
    },
    /**
     * Same as `pipe` in the other direction.
     * Using an API with `pipe(next)` is very easy.
     * Implementing the API with the point of view of the next one registering
     * a source is easier, see the ZipFileWorker.
     * @param {Worker} previous the previous worker, sending events to this one
     * @return {Worker} the current worker for chainability
     */
    registerPrevious : function (previous) {
        if (this.isLocked) {
            throw new Error("The stream '" + this + "' has already been used.");
        }

        // sharing the streamInfo...
        this.streamInfo = previous.streamInfo;
        // ... and adding our own bits
        this.mergeStreamInfo();
        this.previous =  previous;
        var self = this;
        previous.on('data', function (chunk) {
            self.processChunk(chunk);
        });
        previous.on('end', function () {
            self.end();
        });
        previous.on('error', function (e) {
            self.error(e);
        });
        return this;
    },
    /**
     * Pause the stream so it doesn't send events anymore.
     * @return {Boolean} true if this call paused the worker, false otherwise.
     */
    pause : function () {
        if(this.isPaused || this.isFinished) {
            return false;
        }
        this.isPaused = true;

        if(this.previous) {
            this.previous.pause();
        }
        return true;
    },
    /**
     * Resume a paused stream.
     * @return {Boolean} true if this call resumed the worker, false otherwise.
     */
    resume : function () {
        if(!this.isPaused || this.isFinished) {
            return false;
        }
        this.isPaused = false;

        // if true, the worker tried to resume but failed
        var withError = false;
        if(this.generatedError) {
            this.error(this.generatedError);
            withError = true;
        }
        if(this.previous) {
            this.previous.resume();
        }

        return !withError;
    },
    /**
     * Flush any remaining bytes as the stream is ending.
     */
    flush : function () {},
    /**
     * Process a chunk. This is usually the method overridden.
     * @param {Object} chunk the chunk to process.
     */
    processChunk : function(chunk) {
        this.push(chunk);
    },
    /**
     * Add a key/value to be added in the workers chain streamInfo once activated.
     * @param {String} key the key to use
     * @param {Object} value the associated value
     * @return {Worker} the current worker for chainability
     */
    withStreamInfo : function (key, value) {
        this.extraStreamInfo[key] = value;
        this.mergeStreamInfo();
        return this;
    },
    /**
     * Merge this worker's streamInfo into the chain's streamInfo.
     */
    mergeStreamInfo : function () {
        for(var key in this.extraStreamInfo) {
            if (!this.extraStreamInfo.hasOwnProperty(key)) {
                continue;
            }
            this.streamInfo[key] = this.extraStreamInfo[key];
        }
    },

    /**
     * Lock the stream to prevent further updates on the workers chain.
     * After calling this method, all calls to pipe will fail.
     */
    lock: function () {
        if (this.isLocked) {
            throw new Error("The stream '" + this + "' has already been used.");
        }
        this.isLocked = true;
        if (this.previous) {
            this.previous.lock();
        }
    },

    /**
     *
     * Pretty print the workers chain.
     */
    toString : function () {
        var me = "Worker " + this.name;
        if (this.previous) {
            return this.previous + " -> " + me;
        } else {
            return me;
        }
    }
};

module.exports = GenericWorker;

},{}],29:[function(require,module,exports){
'use strict';

var utils = require('../utils');
var ConvertWorker = require('./ConvertWorker');
var GenericWorker = require('./GenericWorker');
var base64 = require('../base64');
var support = require("../support");
var external = require("../external");

var NodejsStreamOutputAdapter = null;
if (support.nodestream) {
    try {
        NodejsStreamOutputAdapter = require('../nodejs/NodejsStreamOutputAdapter');
    } catch(e) {}
}

/**
 * Apply the final transformation of the data. If the user wants a Blob for
 * example, it's easier to work with an U8intArray and finally do the
 * ArrayBuffer/Blob conversion.
 * @param {String} type the name of the final type
 * @param {String|Uint8Array|Buffer} content the content to transform
 * @param {String} mimeType the mime type of the content, if applicable.
 * @return {String|Uint8Array|ArrayBuffer|Buffer|Blob} the content in the right format.
 */
function transformZipOutput(type, content, mimeType) {
    switch(type) {
        case "blob" :
            return utils.newBlob(utils.transformTo("arraybuffer", content), mimeType);
        case "base64" :
            return base64.encode(content);
        default :
            return utils.transformTo(type, content);
    }
}

/**
 * Concatenate an array of data of the given type.
 * @param {String} type the type of the data in the given array.
 * @param {Array} dataArray the array containing the data chunks to concatenate
 * @return {String|Uint8Array|Buffer} the concatenated data
 * @throws Error if the asked type is unsupported
 */
function concat (type, dataArray) {
    var i, index = 0, res = null, totalLength = 0;
    for(i = 0; i < dataArray.length; i++) {
        totalLength += dataArray[i].length;
    }
    switch(type) {
        case "string":
            return dataArray.join("");
          case "array":
            return Array.prototype.concat.apply([], dataArray);
        case "uint8array":
            res = new Uint8Array(totalLength);
            for(i = 0; i < dataArray.length; i++) {
                res.set(dataArray[i], index);
                index += dataArray[i].length;
            }
            return res;
        case "nodebuffer":
            return Buffer.concat(dataArray);
        default:
            throw new Error("concat : unsupported type '"  + type + "'");
    }
}

/**
 * Listen a StreamHelper, accumulate its content and concatenate it into a
 * complete block.
 * @param {StreamHelper} helper the helper to use.
 * @param {Function} updateCallback a callback called on each update. Called
 * with one arg :
 * - the metadata linked to the update received.
 * @return Promise the promise for the accumulation.
 */
function accumulate(helper, updateCallback) {
    return new external.Promise(function (resolve, reject){
        var dataArray = [];
        var chunkType = helper._internalType,
            resultType = helper._outputType,
            mimeType = helper._mimeType;
        helper
        .on('data', function (data, meta) {
            dataArray.push(data);
            if(updateCallback) {
                updateCallback(meta);
            }
        })
        .on('error', function(err) {
            dataArray = [];
            reject(err);
        })
        .on('end', function (){
            try {
                var result = transformZipOutput(resultType, concat(chunkType, dataArray), mimeType);
                resolve(result);
            } catch (e) {
                reject(e);
            }
            dataArray = [];
        })
        .resume();
    });
}

/**
 * An helper to easily use workers outside of JSZip.
 * @constructor
 * @param {Worker} worker the worker to wrap
 * @param {String} outputType the type of data expected by the use
 * @param {String} mimeType the mime type of the content, if applicable.
 */
function StreamHelper(worker, outputType, mimeType) {
    var internalType = outputType;
    switch(outputType) {
        case "blob":
        case "arraybuffer":
            internalType = "uint8array";
        break;
        case "base64":
            internalType = "string";
        break;
    }

    try {
        // the type used internally
        this._internalType = internalType;
        // the type used to output results
        this._outputType = outputType;
        // the mime type
        this._mimeType = mimeType;
        utils.checkSupport(internalType);
        this._worker = worker.pipe(new ConvertWorker(internalType));
        // the last workers can be rewired without issues but we need to
        // prevent any updates on previous workers.
        worker.lock();
    } catch(e) {
        this._worker = new GenericWorker("error");
        this._worker.error(e);
    }
}

StreamHelper.prototype = {
    /**
     * Listen a StreamHelper, accumulate its content and concatenate it into a
     * complete block.
     * @param {Function} updateCb the update callback.
     * @return Promise the promise for the accumulation.
     */
    accumulate : function (updateCb) {
        return accumulate(this, updateCb);
    },
    /**
     * Add a listener on an event triggered on a stream.
     * @param {String} evt the name of the event
     * @param {Function} fn the listener
     * @return {StreamHelper} the current helper.
     */
    on : function (evt, fn) {
        var self = this;

        if(evt === "data") {
            this._worker.on(evt, function (chunk) {
                fn.call(self, chunk.data, chunk.meta);
            });
        } else {
            this._worker.on(evt, function () {
                utils.delay(fn, arguments, self);
            });
        }
        return this;
    },
    /**
     * Resume the flow of chunks.
     * @return {StreamHelper} the current helper.
     */
    resume : function () {
        utils.delay(this._worker.resume, [], this._worker);
        return this;
    },
    /**
     * Pause the flow of chunks.
     * @return {StreamHelper} the current helper.
     */
    pause : function () {
        this._worker.pause();
        return this;
    },
    /**
     * Return a nodejs stream for this helper.
     * @param {Function} updateCb the update callback.
     * @return {NodejsStreamOutputAdapter} the nodejs stream.
     */
    toNodejsStream : function (updateCb) {
        utils.checkSupport("nodestream");
        if (this._outputType !== "nodebuffer") {
            // an object stream containing blob/arraybuffer/uint8array/string
            // is strange and I don't know if it would be useful.
            // I you find this comment and have a good usecase, please open a
            // bug report !
            throw new Error(this._outputType + " is not supported by this method");
        }

        return new NodejsStreamOutputAdapter(this, {
            objectMode : this._outputType !== "nodebuffer"
        }, updateCb);
    }
};


module.exports = StreamHelper;

},{"../base64":1,"../external":6,"../nodejs/NodejsStreamOutputAdapter":13,"../support":30,"../utils":32,"./ConvertWorker":24,"./GenericWorker":28}],30:[function(require,module,exports){
'use strict';

exports.base64 = true;
exports.array = true;
exports.string = true;
exports.arraybuffer = typeof ArrayBuffer !== "undefined" && typeof Uint8Array !== "undefined";
exports.nodebuffer = typeof Buffer !== "undefined";
// contains true if JSZip can read/generate Uint8Array, false otherwise.
exports.uint8array = typeof Uint8Array !== "undefined";

if (typeof ArrayBuffer === "undefined") {
    exports.blob = false;
}
else {
    var buffer = new ArrayBuffer(0);
    try {
        exports.blob = new Blob([buffer], {
            type: "application/zip"
        }).size === 0;
    }
    catch (e) {
        try {
            var Builder = self.BlobBuilder || self.WebKitBlobBuilder || self.MozBlobBuilder || self.MSBlobBuilder;
            var builder = new Builder();
            builder.append(buffer);
            exports.blob = builder.getBlob('application/zip').size === 0;
        }
        catch (e) {
            exports.blob = false;
        }
    }
}

try {
    exports.nodestream = !!require('readable-stream').Readable;
} catch(e) {
    exports.nodestream = false;
}

},{"readable-stream":16}],31:[function(require,module,exports){
'use strict';

var utils = require('./utils');
var support = require('./support');
var nodejsUtils = require('./nodejsUtils');
var GenericWorker = require('./stream/GenericWorker');

/**
 * The following functions come from pako, from pako/lib/utils/strings
 * released under the MIT license, see pako https://github.com/nodeca/pako/
 */

// Table with utf8 lengths (calculated by first byte of sequence)
// Note, that 5 & 6-byte values and some 4-byte values can not be represented in JS,
// because max possible codepoint is 0x10ffff
var _utf8len = new Array(256);
for (var i=0; i<256; i++) {
  _utf8len[i] = (i >= 252 ? 6 : i >= 248 ? 5 : i >= 240 ? 4 : i >= 224 ? 3 : i >= 192 ? 2 : 1);
}
_utf8len[254]=_utf8len[254]=1; // Invalid sequence start

// convert string to array (typed, when possible)
var string2buf = function (str) {
    var buf, c, c2, m_pos, i, str_len = str.length, buf_len = 0;

    // count binary size
    for (m_pos = 0; m_pos < str_len; m_pos++) {
        c = str.charCodeAt(m_pos);
        if ((c & 0xfc00) === 0xd800 && (m_pos+1 < str_len)) {
            c2 = str.charCodeAt(m_pos+1);
            if ((c2 & 0xfc00) === 0xdc00) {
                c = 0x10000 + ((c - 0xd800) << 10) + (c2 - 0xdc00);
                m_pos++;
            }
        }
        buf_len += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
    }

    // allocate buffer
    if (support.uint8array) {
        buf = new Uint8Array(buf_len);
    } else {
        buf = new Array(buf_len);
    }

    // convert
    for (i=0, m_pos = 0; i < buf_len; m_pos++) {
        c = str.charCodeAt(m_pos);
        if ((c & 0xfc00) === 0xd800 && (m_pos+1 < str_len)) {
            c2 = str.charCodeAt(m_pos+1);
            if ((c2 & 0xfc00) === 0xdc00) {
                c = 0x10000 + ((c - 0xd800) << 10) + (c2 - 0xdc00);
                m_pos++;
            }
        }
        if (c < 0x80) {
            /* one byte */
            buf[i++] = c;
        } else if (c < 0x800) {
            /* two bytes */
            buf[i++] = 0xC0 | (c >>> 6);
            buf[i++] = 0x80 | (c & 0x3f);
        } else if (c < 0x10000) {
            /* three bytes */
            buf[i++] = 0xE0 | (c >>> 12);
            buf[i++] = 0x80 | (c >>> 6 & 0x3f);
            buf[i++] = 0x80 | (c & 0x3f);
        } else {
            /* four bytes */
            buf[i++] = 0xf0 | (c >>> 18);
            buf[i++] = 0x80 | (c >>> 12 & 0x3f);
            buf[i++] = 0x80 | (c >>> 6 & 0x3f);
            buf[i++] = 0x80 | (c & 0x3f);
        }
    }

    return buf;
};

// Calculate max possible position in utf8 buffer,
// that will not break sequence. If that's not possible
// - (very small limits) return max size as is.
//
// buf[] - utf8 bytes array
// max   - length limit (mandatory);
var utf8border = function(buf, max) {
    var pos;

    max = max || buf.length;
    if (max > buf.length) { max = buf.length; }

    // go back from last position, until start of sequence found
    pos = max-1;
    while (pos >= 0 && (buf[pos] & 0xC0) === 0x80) { pos--; }

    // Fuckup - very small and broken sequence,
    // return max, because we should return something anyway.
    if (pos < 0) { return max; }

    // If we came to start of buffer - that means vuffer is too small,
    // return max too.
    if (pos === 0) { return max; }

    return (pos + _utf8len[buf[pos]] > max) ? pos : max;
};

// convert array to string
var buf2string = function (buf) {
    var str, i, out, c, c_len;
    var len = buf.length;

    // Reserve max possible length (2 words per char)
    // NB: by unknown reasons, Array is significantly faster for
    //     String.fromCharCode.apply than Uint16Array.
    var utf16buf = new Array(len*2);

    for (out=0, i=0; i<len;) {
        c = buf[i++];
        // quick process ascii
        if (c < 0x80) { utf16buf[out++] = c; continue; }

        c_len = _utf8len[c];
        // skip 5 & 6 byte codes
        if (c_len > 4) { utf16buf[out++] = 0xfffd; i += c_len-1; continue; }

        // apply mask on first byte
        c &= c_len === 2 ? 0x1f : c_len === 3 ? 0x0f : 0x07;
        // join the rest
        while (c_len > 1 && i < len) {
            c = (c << 6) | (buf[i++] & 0x3f);
            c_len--;
        }

        // terminated by end of string?
        if (c_len > 1) { utf16buf[out++] = 0xfffd; continue; }

        if (c < 0x10000) {
            utf16buf[out++] = c;
        } else {
            c -= 0x10000;
            utf16buf[out++] = 0xd800 | ((c >> 10) & 0x3ff);
            utf16buf[out++] = 0xdc00 | (c & 0x3ff);
        }
    }

    // shrinkBuf(utf16buf, out)
    if (utf16buf.length !== out) {
        if(utf16buf.subarray) {
            utf16buf = utf16buf.subarray(0, out);
        } else {
            utf16buf.length = out;
        }
    }

    // return String.fromCharCode.apply(null, utf16buf);
    return utils.applyFromCharCode(utf16buf);
};


// That's all for the pako functions.


/**
 * Transform a javascript string into an array (typed if possible) of bytes,
 * UTF-8 encoded.
 * @param {String} str the string to encode
 * @return {Array|Uint8Array|Buffer} the UTF-8 encoded string.
 */
exports.utf8encode = function utf8encode(str) {
    if (support.nodebuffer) {
        return nodejsUtils.newBufferFrom(str, "utf-8");
    }

    return string2buf(str);
};


/**
 * Transform a bytes array (or a representation) representing an UTF-8 encoded
 * string into a javascript string.
 * @param {Array|Uint8Array|Buffer} buf the data de decode
 * @return {String} the decoded string.
 */
exports.utf8decode = function utf8decode(buf) {
    if (support.nodebuffer) {
        return utils.transformTo("nodebuffer", buf).toString("utf-8");
    }

    buf = utils.transformTo(support.uint8array ? "uint8array" : "array", buf);

    return buf2string(buf);
};

/**
 * A worker to decode utf8 encoded binary chunks into string chunks.
 * @constructor
 */
function Utf8DecodeWorker() {
    GenericWorker.call(this, "utf-8 decode");
    // the last bytes if a chunk didn't end with a complete codepoint.
    this.leftOver = null;
}
utils.inherits(Utf8DecodeWorker, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
Utf8DecodeWorker.prototype.processChunk = function (chunk) {

    var data = utils.transformTo(support.uint8array ? "uint8array" : "array", chunk.data);

    // 1st step, re-use what's left of the previous chunk
    if (this.leftOver && this.leftOver.length) {
        if(support.uint8array) {
            var previousData = data;
            data = new Uint8Array(previousData.length + this.leftOver.length);
            data.set(this.leftOver, 0);
            data.set(previousData, this.leftOver.length);
        } else {
            data = this.leftOver.concat(data);
        }
        this.leftOver = null;
    }

    var nextBoundary = utf8border(data);
    var usableData = data;
    if (nextBoundary !== data.length) {
        if (support.uint8array) {
            usableData = data.subarray(0, nextBoundary);
            this.leftOver = data.subarray(nextBoundary, data.length);
        } else {
            usableData = data.slice(0, nextBoundary);
            this.leftOver = data.slice(nextBoundary, data.length);
        }
    }

    this.push({
        data : exports.utf8decode(usableData),
        meta : chunk.meta
    });
};

/**
 * @see GenericWorker.flush
 */
Utf8DecodeWorker.prototype.flush = function () {
    if(this.leftOver && this.leftOver.length) {
        this.push({
            data : exports.utf8decode(this.leftOver),
            meta : {}
        });
        this.leftOver = null;
    }
};
exports.Utf8DecodeWorker = Utf8DecodeWorker;

/**
 * A worker to endcode string chunks into utf8 encoded binary chunks.
 * @constructor
 */
function Utf8EncodeWorker() {
    GenericWorker.call(this, "utf-8 encode");
}
utils.inherits(Utf8EncodeWorker, GenericWorker);

/**
 * @see GenericWorker.processChunk
 */
Utf8EncodeWorker.prototype.processChunk = function (chunk) {
    this.push({
        data : exports.utf8encode(chunk.data),
        meta : chunk.meta
    });
};
exports.Utf8EncodeWorker = Utf8EncodeWorker;

},{"./nodejsUtils":14,"./stream/GenericWorker":28,"./support":30,"./utils":32}],32:[function(require,module,exports){
'use strict';

var support = require('./support');
var base64 = require('./base64');
var nodejsUtils = require('./nodejsUtils');
var external = require("./external");
require('setimmediate');


/**
 * Convert a string that pass as a "binary string": it should represent a byte
 * array but may have > 255 char codes. Be sure to take only the first byte
 * and returns the byte array.
 * @param {String} str the string to transform.
 * @return {Array|Uint8Array} the string in a binary format.
 */
function string2binary(str) {
    var result = null;
    if (support.uint8array) {
      result = new Uint8Array(str.length);
    } else {
      result = new Array(str.length);
    }
    return stringToArrayLike(str, result);
}

/**
 * Create a new blob with the given content and the given type.
 * @param {String|ArrayBuffer} part the content to put in the blob. DO NOT use
 * an Uint8Array because the stock browser of android 4 won't accept it (it
 * will be silently converted to a string, "[object Uint8Array]").
 *
 * Use only ONE part to build the blob to avoid a memory leak in IE11 / Edge:
 * when a large amount of Array is used to create the Blob, the amount of
 * memory consumed is nearly 100 times the original data amount.
 *
 * @param {String} type the mime type of the blob.
 * @return {Blob} the created blob.
 */
exports.newBlob = function(part, type) {
    exports.checkSupport("blob");

    try {
        // Blob constructor
        return new Blob([part], {
            type: type
        });
    }
    catch (e) {

        try {
            // deprecated, browser only, old way
            var Builder = self.BlobBuilder || self.WebKitBlobBuilder || self.MozBlobBuilder || self.MSBlobBuilder;
            var builder = new Builder();
            builder.append(part);
            return builder.getBlob(type);
        }
        catch (e) {

            // well, fuck ?!
            throw new Error("Bug : can't construct the Blob.");
        }
    }


};
/**
 * The identity function.
 * @param {Object} input the input.
 * @return {Object} the same input.
 */
function identity(input) {
    return input;
}

/**
 * Fill in an array with a string.
 * @param {String} str the string to use.
 * @param {Array|ArrayBuffer|Uint8Array|Buffer} array the array to fill in (will be mutated).
 * @return {Array|ArrayBuffer|Uint8Array|Buffer} the updated array.
 */
function stringToArrayLike(str, array) {
    for (var i = 0; i < str.length; ++i) {
        array[i] = str.charCodeAt(i) & 0xFF;
    }
    return array;
}

/**
 * An helper for the function arrayLikeToString.
 * This contains static information and functions that
 * can be optimized by the browser JIT compiler.
 */
var arrayToStringHelper = {
    /**
     * Transform an array of int into a string, chunk by chunk.
     * See the performances notes on arrayLikeToString.
     * @param {Array|ArrayBuffer|Uint8Array|Buffer} array the array to transform.
     * @param {String} type the type of the array.
     * @param {Integer} chunk the chunk size.
     * @return {String} the resulting string.
     * @throws Error if the chunk is too big for the stack.
     */
    stringifyByChunk: function(array, type, chunk) {
        var result = [], k = 0, len = array.length;
        // shortcut
        if (len <= chunk) {
            return String.fromCharCode.apply(null, array);
        }
        while (k < len) {
            if (type === "array" || type === "nodebuffer") {
                result.push(String.fromCharCode.apply(null, array.slice(k, Math.min(k + chunk, len))));
            }
            else {
                result.push(String.fromCharCode.apply(null, array.subarray(k, Math.min(k + chunk, len))));
            }
            k += chunk;
        }
        return result.join("");
    },
    /**
     * Call String.fromCharCode on every item in the array.
     * This is the naive implementation, which generate A LOT of intermediate string.
     * This should be used when everything else fail.
     * @param {Array|ArrayBuffer|Uint8Array|Buffer} array the array to transform.
     * @return {String} the result.
     */
    stringifyByChar: function(array){
        var resultStr = "";
        for(var i = 0; i < array.length; i++) {
            resultStr += String.fromCharCode(array[i]);
        }
        return resultStr;
    },
    applyCanBeUsed : {
        /**
         * true if the browser accepts to use String.fromCharCode on Uint8Array
         */
        uint8array : (function () {
            try {
                return support.uint8array && String.fromCharCode.apply(null, new Uint8Array(1)).length === 1;
            } catch (e) {
                return false;
            }
        })(),
        /**
         * true if the browser accepts to use String.fromCharCode on nodejs Buffer.
         */
        nodebuffer : (function () {
            try {
                return support.nodebuffer && String.fromCharCode.apply(null, nodejsUtils.allocBuffer(1)).length === 1;
            } catch (e) {
                return false;
            }
        })()
    }
};

/**
 * Transform an array-like object to a string.
 * @param {Array|ArrayBuffer|Uint8Array|Buffer} array the array to transform.
 * @return {String} the result.
 */
function arrayLikeToString(array) {
    // Performances notes :
    // --------------------
    // String.fromCharCode.apply(null, array) is the fastest, see
    // see http://jsperf.com/converting-a-uint8array-to-a-string/2
    // but the stack is limited (and we can get huge arrays !).
    //
    // result += String.fromCharCode(array[i]); generate too many strings !
    //
    // This code is inspired by http://jsperf.com/arraybuffer-to-string-apply-performance/2
    // TODO : we now have workers that split the work. Do we still need that ?
    var chunk = 65536,
        type = exports.getTypeOf(array),
        canUseApply = true;
    if (type === "uint8array") {
        canUseApply = arrayToStringHelper.applyCanBeUsed.uint8array;
    } else if (type === "nodebuffer") {
        canUseApply = arrayToStringHelper.applyCanBeUsed.nodebuffer;
    }

    if (canUseApply) {
        while (chunk > 1) {
            try {
                return arrayToStringHelper.stringifyByChunk(array, type, chunk);
            } catch (e) {
                chunk = Math.floor(chunk / 2);
            }
        }
    }

    // no apply or chunk error : slow and painful algorithm
    // default browser on android 4.*
    return arrayToStringHelper.stringifyByChar(array);
}

exports.applyFromCharCode = arrayLikeToString;


/**
 * Copy the data from an array-like to an other array-like.
 * @param {Array|ArrayBuffer|Uint8Array|Buffer} arrayFrom the origin array.
 * @param {Array|ArrayBuffer|Uint8Array|Buffer} arrayTo the destination array which will be mutated.
 * @return {Array|ArrayBuffer|Uint8Array|Buffer} the updated destination array.
 */
function arrayLikeToArrayLike(arrayFrom, arrayTo) {
    for (var i = 0; i < arrayFrom.length; i++) {
        arrayTo[i] = arrayFrom[i];
    }
    return arrayTo;
}

// a matrix containing functions to transform everything into everything.
var transform = {};

// string to ?
transform["string"] = {
    "string": identity,
    "array": function(input) {
        return stringToArrayLike(input, new Array(input.length));
    },
    "arraybuffer": function(input) {
        return transform["string"]["uint8array"](input).buffer;
    },
    "uint8array": function(input) {
        return stringToArrayLike(input, new Uint8Array(input.length));
    },
    "nodebuffer": function(input) {
        return stringToArrayLike(input, nodejsUtils.allocBuffer(input.length));
    }
};

// array to ?
transform["array"] = {
    "string": arrayLikeToString,
    "array": identity,
    "arraybuffer": function(input) {
        return (new Uint8Array(input)).buffer;
    },
    "uint8array": function(input) {
        return new Uint8Array(input);
    },
    "nodebuffer": function(input) {
        return nodejsUtils.newBufferFrom(input);
    }
};

// arraybuffer to ?
transform["arraybuffer"] = {
    "string": function(input) {
        return arrayLikeToString(new Uint8Array(input));
    },
    "array": function(input) {
        return arrayLikeToArrayLike(new Uint8Array(input), new Array(input.byteLength));
    },
    "arraybuffer": identity,
    "uint8array": function(input) {
        return new Uint8Array(input);
    },
    "nodebuffer": function(input) {
        return nodejsUtils.newBufferFrom(new Uint8Array(input));
    }
};

// uint8array to ?
transform["uint8array"] = {
    "string": arrayLikeToString,
    "array": function(input) {
        return arrayLikeToArrayLike(input, new Array(input.length));
    },
    "arraybuffer": function(input) {
        return input.buffer;
    },
    "uint8array": identity,
    "nodebuffer": function(input) {
        return nodejsUtils.newBufferFrom(input);
    }
};

// nodebuffer to ?
transform["nodebuffer"] = {
    "string": arrayLikeToString,
    "array": function(input) {
        return arrayLikeToArrayLike(input, new Array(input.length));
    },
    "arraybuffer": function(input) {
        return transform["nodebuffer"]["uint8array"](input).buffer;
    },
    "uint8array": function(input) {
        return arrayLikeToArrayLike(input, new Uint8Array(input.length));
    },
    "nodebuffer": identity
};

/**
 * Transform an input into any type.
 * The supported output type are : string, array, uint8array, arraybuffer, nodebuffer.
 * If no output type is specified, the unmodified input will be returned.
 * @param {String} outputType the output type.
 * @param {String|Array|ArrayBuffer|Uint8Array|Buffer} input the input to convert.
 * @throws {Error} an Error if the browser doesn't support the requested output type.
 */
exports.transformTo = function(outputType, input) {
    if (!input) {
        // undefined, null, etc
        // an empty string won't harm.
        input = "";
    }
    if (!outputType) {
        return input;
    }
    exports.checkSupport(outputType);
    var inputType = exports.getTypeOf(input);
    var result = transform[inputType][outputType](input);
    return result;
};

/**
 * Resolve all relative path components, "." and "..", in a path. If these relative components
 * traverse above the root then the resulting path will only contain the final path component.
 *
 * All empty components, e.g. "//", are removed.
 * @param {string} path A path with / or \ separators
 * @returns {string} The path with all relative path components resolved.
 */
exports.resolve = function(path) {
    var parts = path.split("/");
    var result = [];
    for (var index = 0; index < parts.length; index++) {
        var part = parts[index];
        // Allow the first and last component to be empty for trailing slashes.
        if (part === "." || (part === "" && index !== 0 && index !== parts.length - 1)) {
            continue;
        } else if (part === "..") {
            result.pop();
        } else {
            result.push(part);
        }
    }
    return result.join("/");
};

/**
 * Return the type of the input.
 * The type will be in a format valid for JSZip.utils.transformTo : string, array, uint8array, arraybuffer.
 * @param {Object} input the input to identify.
 * @return {String} the (lowercase) type of the input.
 */
exports.getTypeOf = function(input) {
    if (typeof input === "string") {
        return "string";
    }
    if (Object.prototype.toString.call(input) === "[object Array]") {
        return "array";
    }
    if (support.nodebuffer && nodejsUtils.isBuffer(input)) {
        return "nodebuffer";
    }
    if (support.uint8array && input instanceof Uint8Array) {
        return "uint8array";
    }
    if (support.arraybuffer && input instanceof ArrayBuffer) {
        return "arraybuffer";
    }
};

/**
 * Throw an exception if the type is not supported.
 * @param {String} type the type to check.
 * @throws {Error} an Error if the browser doesn't support the requested type.
 */
exports.checkSupport = function(type) {
    var supported = support[type.toLowerCase()];
    if (!supported) {
        throw new Error(type + " is not supported by this platform");
    }
};

exports.MAX_VALUE_16BITS = 65535;
exports.MAX_VALUE_32BITS = -1; // well, "\xFF\xFF\xFF\xFF\xFF\xFF\xFF\xFF" is parsed as -1

/**
 * Prettify a string read as binary.
 * @param {string} str the string to prettify.
 * @return {string} a pretty string.
 */
exports.pretty = function(str) {
    var res = '',
        code, i;
    for (i = 0; i < (str || "").length; i++) {
        code = str.charCodeAt(i);
        res += '\\x' + (code < 16 ? "0" : "") + code.toString(16).toUpperCase();
    }
    return res;
};

/**
 * Defer the call of a function.
 * @param {Function} callback the function to call asynchronously.
 * @param {Array} args the arguments to give to the callback.
 */
exports.delay = function(callback, args, self) {
    setImmediate(function () {
        callback.apply(self || null, args || []);
    });
};

/**
 * Extends a prototype with an other, without calling a constructor with
 * side effects. Inspired by nodejs' `utils.inherits`
 * @param {Function} ctor the constructor to augment
 * @param {Function} superCtor the parent constructor to use
 */
exports.inherits = function (ctor, superCtor) {
    var Obj = function() {};
    Obj.prototype = superCtor.prototype;
    ctor.prototype = new Obj();
};

/**
 * Merge the objects passed as parameters into a new one.
 * @private
 * @param {...Object} var_args All objects to merge.
 * @return {Object} a new object with the data of the others.
 */
exports.extend = function() {
    var result = {}, i, attr;
    for (i = 0; i < arguments.length; i++) { // arguments is not enumerable in some browsers
        for (attr in arguments[i]) {
            if (arguments[i].hasOwnProperty(attr) && typeof result[attr] === "undefined") {
                result[attr] = arguments[i][attr];
            }
        }
    }
    return result;
};

/**
 * Transform arbitrary content into a Promise.
 * @param {String} name a name for the content being processed.
 * @param {Object} inputData the content to process.
 * @param {Boolean} isBinary true if the content is not an unicode string
 * @param {Boolean} isOptimizedBinaryString true if the string content only has one byte per character.
 * @param {Boolean} isBase64 true if the string content is encoded with base64.
 * @return {Promise} a promise in a format usable by JSZip.
 */
exports.prepareContent = function(name, inputData, isBinary, isOptimizedBinaryString, isBase64) {

    // if inputData is already a promise, this flatten it.
    var promise = external.Promise.resolve(inputData).then(function(data) {


        var isBlob = support.blob && (data instanceof Blob || ['[object File]', '[object Blob]'].indexOf(Object.prototype.toString.call(data)) !== -1);

        if (isBlob && typeof FileReader !== "undefined") {
            return new external.Promise(function (resolve, reject) {
                var reader = new FileReader();

                reader.onload = function(e) {
                    resolve(e.target.result);
                };
                reader.onerror = function(e) {
                    reject(e.target.error);
                };
                reader.readAsArrayBuffer(data);
            });
        } else {
            return data;
        }
    });

    return promise.then(function(data) {
        var dataType = exports.getTypeOf(data);

        if (!dataType) {
            return external.Promise.reject(
                new Error("Can't read the data of '" + name + "'. Is it " +
                          "in a supported JavaScript type (String, Blob, ArrayBuffer, etc) ?")
            );
        }
        // special case : it's way easier to work with Uint8Array than with ArrayBuffer
        if (dataType === "arraybuffer") {
            data = exports.transformTo("uint8array", data);
        } else if (dataType === "string") {
            if (isBase64) {
                data = base64.decode(data);
            }
            else if (isBinary) {
                // optimizedBinaryString === true means that the file has already been filtered with a 0xFF mask
                if (isOptimizedBinaryString !== true) {
                    // this is a string, not in a base64 format.
                    // Be sure that this is a correct "binary string"
                    data = string2binary(data);
                }
            }
        }
        return data;
    });
};

},{"./base64":1,"./external":6,"./nodejsUtils":14,"./support":30,"setimmediate":54}],33:[function(require,module,exports){
'use strict';
var readerFor = require('./reader/readerFor');
var utils = require('./utils');
var sig = require('./signature');
var ZipEntry = require('./zipEntry');
var utf8 = require('./utf8');
var support = require('./support');
//  class ZipEntries {{{
/**
 * All the entries in the zip file.
 * @constructor
 * @param {Object} loadOptions Options for loading the stream.
 */
function ZipEntries(loadOptions) {
    this.files = [];
    this.loadOptions = loadOptions;
}
ZipEntries.prototype = {
    /**
     * Check that the reader is on the specified signature.
     * @param {string} expectedSignature the expected signature.
     * @throws {Error} if it is an other signature.
     */
    checkSignature: function(expectedSignature) {
        if (!this.reader.readAndCheckSignature(expectedSignature)) {
            this.reader.index -= 4;
            var signature = this.reader.readString(4);
            throw new Error("Corrupted zip or bug: unexpected signature " + "(" + utils.pretty(signature) + ", expected " + utils.pretty(expectedSignature) + ")");
        }
    },
    /**
     * Check if the given signature is at the given index.
     * @param {number} askedIndex the index to check.
     * @param {string} expectedSignature the signature to expect.
     * @return {boolean} true if the signature is here, false otherwise.
     */
    isSignature: function(askedIndex, expectedSignature) {
        var currentIndex = this.reader.index;
        this.reader.setIndex(askedIndex);
        var signature = this.reader.readString(4);
        var result = signature === expectedSignature;
        this.reader.setIndex(currentIndex);
        return result;
    },
    /**
     * Read the end of the central directory.
     */
    readBlockEndOfCentral: function() {
        this.diskNumber = this.reader.readInt(2);
        this.diskWithCentralDirStart = this.reader.readInt(2);
        this.centralDirRecordsOnThisDisk = this.reader.readInt(2);
        this.centralDirRecords = this.reader.readInt(2);
        this.centralDirSize = this.reader.readInt(4);
        this.centralDirOffset = this.reader.readInt(4);

        this.zipCommentLength = this.reader.readInt(2);
        // warning : the encoding depends of the system locale
        // On a linux machine with LANG=en_US.utf8, this field is utf8 encoded.
        // On a windows machine, this field is encoded with the localized windows code page.
        var zipComment = this.reader.readData(this.zipCommentLength);
        var decodeParamType = support.uint8array ? "uint8array" : "array";
        // To get consistent behavior with the generation part, we will assume that
        // this is utf8 encoded unless specified otherwise.
        var decodeContent = utils.transformTo(decodeParamType, zipComment);
        this.zipComment = this.loadOptions.decodeFileName(decodeContent);
    },
    /**
     * Read the end of the Zip 64 central directory.
     * Not merged with the method readEndOfCentral :
     * The end of central can coexist with its Zip64 brother,
     * I don't want to read the wrong number of bytes !
     */
    readBlockZip64EndOfCentral: function() {
        this.zip64EndOfCentralSize = this.reader.readInt(8);
        this.reader.skip(4);
        // this.versionMadeBy = this.reader.readString(2);
        // this.versionNeeded = this.reader.readInt(2);
        this.diskNumber = this.reader.readInt(4);
        this.diskWithCentralDirStart = this.reader.readInt(4);
        this.centralDirRecordsOnThisDisk = this.reader.readInt(8);
        this.centralDirRecords = this.reader.readInt(8);
        this.centralDirSize = this.reader.readInt(8);
        this.centralDirOffset = this.reader.readInt(8);

        this.zip64ExtensibleData = {};
        var extraDataSize = this.zip64EndOfCentralSize - 44,
            index = 0,
            extraFieldId,
            extraFieldLength,
            extraFieldValue;
        while (index < extraDataSize) {
            extraFieldId = this.reader.readInt(2);
            extraFieldLength = this.reader.readInt(4);
            extraFieldValue = this.reader.readData(extraFieldLength);
            this.zip64ExtensibleData[extraFieldId] = {
                id: extraFieldId,
                length: extraFieldLength,
                value: extraFieldValue
            };
        }
    },
    /**
     * Read the end of the Zip 64 central directory locator.
     */
    readBlockZip64EndOfCentralLocator: function() {
        this.diskWithZip64CentralDirStart = this.reader.readInt(4);
        this.relativeOffsetEndOfZip64CentralDir = this.reader.readInt(8);
        this.disksCount = this.reader.readInt(4);
        if (this.disksCount > 1) {
            throw new Error("Multi-volumes zip are not supported");
        }
    },
    /**
     * Read the local files, based on the offset read in the central part.
     */
    readLocalFiles: function() {
        var i, file;
        for (i = 0; i < this.files.length; i++) {
            file = this.files[i];
            this.reader.setIndex(file.localHeaderOffset);
            this.checkSignature(sig.LOCAL_FILE_HEADER);
            file.readLocalPart(this.reader);
            file.handleUTF8();
            file.processAttributes();
        }
    },
    /**
     * Read the central directory.
     */
    readCentralDir: function() {
        var file;

        this.reader.setIndex(this.centralDirOffset);
        while (this.reader.readAndCheckSignature(sig.CENTRAL_FILE_HEADER)) {
            file = new ZipEntry({
                zip64: this.zip64
            }, this.loadOptions);
            file.readCentralPart(this.reader);
            this.files.push(file);
        }

        if (this.centralDirRecords !== this.files.length) {
            if (this.centralDirRecords !== 0 && this.files.length === 0) {
                // We expected some records but couldn't find ANY.
                // This is really suspicious, as if something went wrong.
                throw new Error("Corrupted zip or bug: expected " + this.centralDirRecords + " records in central dir, got " + this.files.length);
            } else {
                // We found some records but not all.
                // Something is wrong but we got something for the user: no error here.
                // console.warn("expected", this.centralDirRecords, "records in central dir, got", this.files.length);
            }
        }
    },
    /**
     * Read the end of central directory.
     */
    readEndOfCentral: function() {
        var offset = this.reader.lastIndexOfSignature(sig.CENTRAL_DIRECTORY_END);
        if (offset < 0) {
            // Check if the content is a truncated zip or complete garbage.
            // A "LOCAL_FILE_HEADER" is not required at the beginning (auto
            // extractible zip for example) but it can give a good hint.
            // If an ajax request was used without responseType, we will also
            // get unreadable data.
            var isGarbage = !this.isSignature(0, sig.LOCAL_FILE_HEADER);

            if (isGarbage) {
                throw new Error("Can't find end of central directory : is this a zip file ? " +
                                "If it is, see https://stuk.github.io/jszip/documentation/howto/read_zip.html");
            } else {
                throw new Error("Corrupted zip: can't find end of central directory");
            }

        }
        this.reader.setIndex(offset);
        var endOfCentralDirOffset = offset;
        this.checkSignature(sig.CENTRAL_DIRECTORY_END);
        this.readBlockEndOfCentral();


        /* extract from the zip spec :
            4)  If one of the fields in the end of central directory
                record is too small to hold required data, the field
                should be set to -1 (0xFFFF or 0xFFFFFFFF) and the
                ZIP64 format record should be created.
            5)  The end of central directory record and the
                Zip64 end of central directory locator record must
                reside on the same disk when splitting or spanning
                an archive.
         */
        if (this.diskNumber === utils.MAX_VALUE_16BITS || this.diskWithCentralDirStart === utils.MAX_VALUE_16BITS || this.centralDirRecordsOnThisDisk === utils.MAX_VALUE_16BITS || this.centralDirRecords === utils.MAX_VALUE_16BITS || this.centralDirSize === utils.MAX_VALUE_32BITS || this.centralDirOffset === utils.MAX_VALUE_32BITS) {
            this.zip64 = true;

            /*
            Warning : the zip64 extension is supported, but ONLY if the 64bits integer read from
            the zip file can fit into a 32bits integer. This cannot be solved : JavaScript represents
            all numbers as 64-bit double precision IEEE 754 floating point numbers.
            So, we have 53bits for integers and bitwise operations treat everything as 32bits.
            see https://developer.mozilla.org/en-US/docs/JavaScript/Reference/Operators/Bitwise_Operators
            and http://www.ecma-international.org/publications/files/ECMA-ST/ECMA-262.pdf section 8.5
            */

            // should look for a zip64 EOCD locator
            offset = this.reader.lastIndexOfSignature(sig.ZIP64_CENTRAL_DIRECTORY_LOCATOR);
            if (offset < 0) {
                throw new Error("Corrupted zip: can't find the ZIP64 end of central directory locator");
            }
            this.reader.setIndex(offset);
            this.checkSignature(sig.ZIP64_CENTRAL_DIRECTORY_LOCATOR);
            this.readBlockZip64EndOfCentralLocator();

            // now the zip64 EOCD record
            if (!this.isSignature(this.relativeOffsetEndOfZip64CentralDir, sig.ZIP64_CENTRAL_DIRECTORY_END)) {
                // console.warn("ZIP64 end of central directory not where expected.");
                this.relativeOffsetEndOfZip64CentralDir = this.reader.lastIndexOfSignature(sig.ZIP64_CENTRAL_DIRECTORY_END);
                if (this.relativeOffsetEndOfZip64CentralDir < 0) {
                    throw new Error("Corrupted zip: can't find the ZIP64 end of central directory");
                }
            }
            this.reader.setIndex(this.relativeOffsetEndOfZip64CentralDir);
            this.checkSignature(sig.ZIP64_CENTRAL_DIRECTORY_END);
            this.readBlockZip64EndOfCentral();
        }

        var expectedEndOfCentralDirOffset = this.centralDirOffset + this.centralDirSize;
        if (this.zip64) {
            expectedEndOfCentralDirOffset += 20; // end of central dir 64 locator
            expectedEndOfCentralDirOffset += 12 /* should not include the leading 12 bytes */ + this.zip64EndOfCentralSize;
        }

        var extraBytes = endOfCentralDirOffset - expectedEndOfCentralDirOffset;

        if (extraBytes > 0) {
            // console.warn(extraBytes, "extra bytes at beginning or within zipfile");
            if (this.isSignature(endOfCentralDirOffset, sig.CENTRAL_FILE_HEADER)) {
                // The offsets seem wrong, but we have something at the specified offset.
                // So… we keep it.
            } else {
                // the offset is wrong, update the "zero" of the reader
                // this happens if data has been prepended (crx files for example)
                this.reader.zero = extraBytes;
            }
        } else if (extraBytes < 0) {
            throw new Error("Corrupted zip: missing " + Math.abs(extraBytes) + " bytes.");
        }
    },
    prepareReader: function(data) {
        this.reader = readerFor(data);
    },
    /**
     * Read a zip file and create ZipEntries.
     * @param {String|ArrayBuffer|Uint8Array|Buffer} data the binary string representing a zip file.
     */
    load: function(data) {
        this.prepareReader(data);
        this.readEndOfCentral();
        this.readCentralDir();
        this.readLocalFiles();
    }
};
// }}} end of ZipEntries
module.exports = ZipEntries;

},{"./reader/readerFor":22,"./signature":23,"./support":30,"./utf8":31,"./utils":32,"./zipEntry":34}],34:[function(require,module,exports){
'use strict';
var readerFor = require('./reader/readerFor');
var utils = require('./utils');
var CompressedObject = require('./compressedObject');
var crc32fn = require('./crc32');
var utf8 = require('./utf8');
var compressions = require('./compressions');
var support = require('./support');

var MADE_BY_DOS = 0x00;
var MADE_BY_UNIX = 0x03;

/**
 * Find a compression registered in JSZip.
 * @param {string} compressionMethod the method magic to find.
 * @return {Object|null} the JSZip compression object, null if none found.
 */
var findCompression = function(compressionMethod) {
    for (var method in compressions) {
        if (!compressions.hasOwnProperty(method)) {
            continue;
        }
        if (compressions[method].magic === compressionMethod) {
            return compressions[method];
        }
    }
    return null;
};

// class ZipEntry {{{
/**
 * An entry in the zip file.
 * @constructor
 * @param {Object} options Options of the current file.
 * @param {Object} loadOptions Options for loading the stream.
 */
function ZipEntry(options, loadOptions) {
    this.options = options;
    this.loadOptions = loadOptions;
}
ZipEntry.prototype = {
    /**
     * say if the file is encrypted.
     * @return {boolean} true if the file is encrypted, false otherwise.
     */
    isEncrypted: function() {
        // bit 1 is set
        return (this.bitFlag & 0x0001) === 0x0001;
    },
    /**
     * say if the file has utf-8 filename/comment.
     * @return {boolean} true if the filename/comment is in utf-8, false otherwise.
     */
    useUTF8: function() {
        // bit 11 is set
        return (this.bitFlag & 0x0800) === 0x0800;
    },
    /**
     * Read the local part of a zip file and add the info in this object.
     * @param {DataReader} reader the reader to use.
     */
    readLocalPart: function(reader) {
        var compression, localExtraFieldsLength;

        // we already know everything from the central dir !
        // If the central dir data are false, we are doomed.
        // On the bright side, the local part is scary  : zip64, data descriptors, both, etc.
        // The less data we get here, the more reliable this should be.
        // Let's skip the whole header and dash to the data !
        reader.skip(22);
        // in some zip created on windows, the filename stored in the central dir contains \ instead of /.
        // Strangely, the filename here is OK.
        // I would love to treat these zip files as corrupted (see http://www.info-zip.org/FAQ.html#backslashes
        // or APPNOTE#4.4.17.1, "All slashes MUST be forward slashes '/'") but there are a lot of bad zip generators...
        // Search "unzip mismatching "local" filename continuing with "central" filename version" on
        // the internet.
        //
        // I think I see the logic here : the central directory is used to display
        // content and the local directory is used to extract the files. Mixing / and \
        // may be used to display \ to windows users and use / when extracting the files.
        // Unfortunately, this lead also to some issues : http://seclists.org/fulldisclosure/2009/Sep/394
        this.fileNameLength = reader.readInt(2);
        localExtraFieldsLength = reader.readInt(2); // can't be sure this will be the same as the central dir
        // the fileName is stored as binary data, the handleUTF8 method will take care of the encoding.
        this.fileName = reader.readData(this.fileNameLength);
        reader.skip(localExtraFieldsLength);

        if (this.compressedSize === -1 || this.uncompressedSize === -1) {
            throw new Error("Bug or corrupted zip : didn't get enough information from the central directory " + "(compressedSize === -1 || uncompressedSize === -1)");
        }

        compression = findCompression(this.compressionMethod);
        if (compression === null) { // no compression found
            throw new Error("Corrupted zip : compression " + utils.pretty(this.compressionMethod) + " unknown (inner file : " + utils.transformTo("string", this.fileName) + ")");
        }
        this.decompressed = new CompressedObject(this.compressedSize, this.uncompressedSize, this.crc32, compression, reader.readData(this.compressedSize));
    },

    /**
     * Read the central part of a zip file and add the info in this object.
     * @param {DataReader} reader the reader to use.
     */
    readCentralPart: function(reader) {
        this.versionMadeBy = reader.readInt(2);
        reader.skip(2);
        // this.versionNeeded = reader.readInt(2);
        this.bitFlag = reader.readInt(2);
        this.compressionMethod = reader.readString(2);
        this.date = reader.readDate();
        this.crc32 = reader.readInt(4);
        this.compressedSize = reader.readInt(4);
        this.uncompressedSize = reader.readInt(4);
        var fileNameLength = reader.readInt(2);
        this.extraFieldsLength = reader.readInt(2);
        this.fileCommentLength = reader.readInt(2);
        this.diskNumberStart = reader.readInt(2);
        this.internalFileAttributes = reader.readInt(2);
        this.externalFileAttributes = reader.readInt(4);
        this.localHeaderOffset = reader.readInt(4);

        if (this.isEncrypted()) {
            throw new Error("Encrypted zip are not supported");
        }

        // will be read in the local part, see the comments there
        reader.skip(fileNameLength);
        this.readExtraFields(reader);
        this.parseZIP64ExtraField(reader);
        this.fileComment = reader.readData(this.fileCommentLength);
    },

    /**
     * Parse the external file attributes and get the unix/dos permissions.
     */
    processAttributes: function () {
        this.unixPermissions = null;
        this.dosPermissions = null;
        var madeBy = this.versionMadeBy >> 8;

        // Check if we have the DOS directory flag set.
        // We look for it in the DOS and UNIX permissions
        // but some unknown platform could set it as a compatibility flag.
        this.dir = this.externalFileAttributes & 0x0010 ? true : false;

        if(madeBy === MADE_BY_DOS) {
            // first 6 bits (0 to 5)
            this.dosPermissions = this.externalFileAttributes & 0x3F;
        }

        if(madeBy === MADE_BY_UNIX) {
            this.unixPermissions = (this.externalFileAttributes >> 16) & 0xFFFF;
            // the octal permissions are in (this.unixPermissions & 0x01FF).toString(8);
        }

        // fail safe : if the name ends with a / it probably means a folder
        if (!this.dir && this.fileNameStr.slice(-1) === '/') {
            this.dir = true;
        }
    },

    /**
     * Parse the ZIP64 extra field and merge the info in the current ZipEntry.
     * @param {DataReader} reader the reader to use.
     */
    parseZIP64ExtraField: function(reader) {

        if (!this.extraFields[0x0001]) {
            return;
        }

        // should be something, preparing the extra reader
        var extraReader = readerFor(this.extraFields[0x0001].value);

        // I really hope that these 64bits integer can fit in 32 bits integer, because js
        // won't let us have more.
        if (this.uncompressedSize === utils.MAX_VALUE_32BITS) {
            this.uncompressedSize = extraReader.readInt(8);
        }
        if (this.compressedSize === utils.MAX_VALUE_32BITS) {
            this.compressedSize = extraReader.readInt(8);
        }
        if (this.localHeaderOffset === utils.MAX_VALUE_32BITS) {
            this.localHeaderOffset = extraReader.readInt(8);
        }
        if (this.diskNumberStart === utils.MAX_VALUE_32BITS) {
            this.diskNumberStart = extraReader.readInt(4);
        }
    },
    /**
     * Read the central part of a zip file and add the info in this object.
     * @param {DataReader} reader the reader to use.
     */
    readExtraFields: function(reader) {
        var end = reader.index + this.extraFieldsLength,
            extraFieldId,
            extraFieldLength,
            extraFieldValue;

        if (!this.extraFields) {
            this.extraFields = {};
        }

        while (reader.index + 4 < end) {
            extraFieldId = reader.readInt(2);
            extraFieldLength = reader.readInt(2);
            extraFieldValue = reader.readData(extraFieldLength);

            this.extraFields[extraFieldId] = {
                id: extraFieldId,
                length: extraFieldLength,
                value: extraFieldValue
            };
        }

        reader.setIndex(end);
    },
    /**
     * Apply an UTF8 transformation if needed.
     */
    handleUTF8: function() {
        var decodeParamType = support.uint8array ? "uint8array" : "array";
        if (this.useUTF8()) {
            this.fileNameStr = utf8.utf8decode(this.fileName);
            this.fileCommentStr = utf8.utf8decode(this.fileComment);
        } else {
            var upath = this.findExtraFieldUnicodePath();
            if (upath !== null) {
                this.fileNameStr = upath;
            } else {
                // ASCII text or unsupported code page
                var fileNameByteArray =  utils.transformTo(decodeParamType, this.fileName);
                this.fileNameStr = this.loadOptions.decodeFileName(fileNameByteArray);
            }

            var ucomment = this.findExtraFieldUnicodeComment();
            if (ucomment !== null) {
                this.fileCommentStr = ucomment;
            } else {
                // ASCII text or unsupported code page
                var commentByteArray =  utils.transformTo(decodeParamType, this.fileComment);
                this.fileCommentStr = this.loadOptions.decodeFileName(commentByteArray);
            }
        }
    },

    /**
     * Find the unicode path declared in the extra field, if any.
     * @return {String} the unicode path, null otherwise.
     */
    findExtraFieldUnicodePath: function() {
        var upathField = this.extraFields[0x7075];
        if (upathField) {
            var extraReader = readerFor(upathField.value);

            // wrong version
            if (extraReader.readInt(1) !== 1) {
                return null;
            }

            // the crc of the filename changed, this field is out of date.
            if (crc32fn(this.fileName) !== extraReader.readInt(4)) {
                return null;
            }

            return utf8.utf8decode(extraReader.readData(upathField.length - 5));
        }
        return null;
    },

    /**
     * Find the unicode comment declared in the extra field, if any.
     * @return {String} the unicode comment, null otherwise.
     */
    findExtraFieldUnicodeComment: function() {
        var ucommentField = this.extraFields[0x6375];
        if (ucommentField) {
            var extraReader = readerFor(ucommentField.value);

            // wrong version
            if (extraReader.readInt(1) !== 1) {
                return null;
            }

            // the crc of the comment changed, this field is out of date.
            if (crc32fn(this.fileComment) !== extraReader.readInt(4)) {
                return null;
            }

            return utf8.utf8decode(extraReader.readData(ucommentField.length - 5));
        }
        return null;
    }
};
module.exports = ZipEntry;

},{"./compressedObject":2,"./compressions":3,"./crc32":4,"./reader/readerFor":22,"./support":30,"./utf8":31,"./utils":32}],35:[function(require,module,exports){
'use strict';

var StreamHelper = require('./stream/StreamHelper');
var DataWorker = require('./stream/DataWorker');
var utf8 = require('./utf8');
var CompressedObject = require('./compressedObject');
var GenericWorker = require('./stream/GenericWorker');

/**
 * A simple object representing a file in the zip file.
 * @constructor
 * @param {string} name the name of the file
 * @param {String|ArrayBuffer|Uint8Array|Buffer} data the data
 * @param {Object} options the options of the file
 */
var ZipObject = function(name, data, options) {
    this.name = name;
    this.dir = options.dir;
    this.date = options.date;
    this.comment = options.comment;
    this.unixPermissions = options.unixPermissions;
    this.dosPermissions = options.dosPermissions;

    this._data = data;
    this._dataBinary = options.binary;
    // keep only the compression
    this.options = {
        compression : options.compression,
        compressionOptions : options.compressionOptions
    };
};

ZipObject.prototype = {
    /**
     * Create an internal stream for the content of this object.
     * @param {String} type the type of each chunk.
     * @return StreamHelper the stream.
     */
    internalStream: function (type) {
        var result = null, outputType = "string";
        try {
            if (!type) {
                throw new Error("No output type specified.");
            }
            outputType = type.toLowerCase();
            var askUnicodeString = outputType === "string" || outputType === "text";
            if (outputType === "binarystring" || outputType === "text") {
                outputType = "string";
            }
            result = this._decompressWorker();

            var isUnicodeString = !this._dataBinary;

            if (isUnicodeString && !askUnicodeString) {
                result = result.pipe(new utf8.Utf8EncodeWorker());
            }
            if (!isUnicodeString && askUnicodeString) {
                result = result.pipe(new utf8.Utf8DecodeWorker());
            }
        } catch (e) {
            result = new GenericWorker("error");
            result.error(e);
        }

        return new StreamHelper(result, outputType, "");
    },

    /**
     * Prepare the content in the asked type.
     * @param {String} type the type of the result.
     * @param {Function} onUpdate a function to call on each internal update.
     * @return Promise the promise of the result.
     */
    async: function (type, onUpdate) {
        return this.internalStream(type).accumulate(onUpdate);
    },

    /**
     * Prepare the content as a nodejs stream.
     * @param {String} type the type of each chunk.
     * @param {Function} onUpdate a function to call on each internal update.
     * @return Stream the stream.
     */
    nodeStream: function (type, onUpdate) {
        return this.internalStream(type || "nodebuffer").toNodejsStream(onUpdate);
    },

    /**
     * Return a worker for the compressed content.
     * @private
     * @param {Object} compression the compression object to use.
     * @param {Object} compressionOptions the options to use when compressing.
     * @return Worker the worker.
     */
    _compressWorker: function (compression, compressionOptions) {
        if (
            this._data instanceof CompressedObject &&
            this._data.compression.magic === compression.magic
        ) {
            return this._data.getCompressedWorker();
        } else {
            var result = this._decompressWorker();
            if(!this._dataBinary) {
                result = result.pipe(new utf8.Utf8EncodeWorker());
            }
            return CompressedObject.createWorkerFrom(result, compression, compressionOptions);
        }
    },
    /**
     * Return a worker for the decompressed content.
     * @private
     * @return Worker the worker.
     */
    _decompressWorker : function () {
        if (this._data instanceof CompressedObject) {
            return this._data.getContentWorker();
        } else if (this._data instanceof GenericWorker) {
            return this._data;
        } else {
            return new DataWorker(this._data);
        }
    }
};

var removedMethods = ["asText", "asBinary", "asNodeBuffer", "asUint8Array", "asArrayBuffer"];
var removedFn = function () {
    throw new Error("This method has been removed in JSZip 3.0, please check the upgrade guide.");
};

for(var i = 0; i < removedMethods.length; i++) {
    ZipObject.prototype[removedMethods[i]] = removedFn;
}
module.exports = ZipObject;

},{"./compressedObject":2,"./stream/DataWorker":27,"./stream/GenericWorker":28,"./stream/StreamHelper":29,"./utf8":31}],36:[function(require,module,exports){
(function (global){
'use strict';
var Mutation = global.MutationObserver || global.WebKitMutationObserver;

var scheduleDrain;

{
  if (Mutation) {
    var called = 0;
    var observer = new Mutation(nextTick);
    var element = global.document.createTextNode('');
    observer.observe(element, {
      characterData: true
    });
    scheduleDrain = function () {
      element.data = (called = ++called % 2);
    };
  } else if (!global.setImmediate && typeof global.MessageChannel !== 'undefined') {
    var channel = new global.MessageChannel();
    channel.port1.onmessage = nextTick;
    scheduleDrain = function () {
      channel.port2.postMessage(0);
    };
  } else if ('document' in global && 'onreadystatechange' in global.document.createElement('script')) {
    scheduleDrain = function () {

      // Create a <script> element; its readystatechange event will be fired asynchronously once it is inserted
      // into the document. Do so, thus queuing up the task. Remember to clean up once it's been called.
      var scriptEl = global.document.createElement('script');
      scriptEl.onreadystatechange = function () {
        nextTick();

        scriptEl.onreadystatechange = null;
        scriptEl.parentNode.removeChild(scriptEl);
        scriptEl = null;
      };
      global.document.documentElement.appendChild(scriptEl);
    };
  } else {
    scheduleDrain = function () {
      setTimeout(nextTick, 0);
    };
  }
}

var draining;
var queue = [];
//named nextTick for less confusing stack traces
function nextTick() {
  draining = true;
  var i, oldQueue;
  var len = queue.length;
  while (len) {
    oldQueue = queue;
    queue = [];
    i = -1;
    while (++i < len) {
      oldQueue[i]();
    }
    len = queue.length;
  }
  draining = false;
}

module.exports = immediate;
function immediate(task) {
  if (queue.push(task) === 1 && !draining) {
    scheduleDrain();
  }
}

}).call(this,typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : {})
},{}],37:[function(require,module,exports){
'use strict';
var immediate = require('immediate');

/* istanbul ignore next */
function INTERNAL() {}

var handlers = {};

var REJECTED = ['REJECTED'];
var FULFILLED = ['FULFILLED'];
var PENDING = ['PENDING'];

module.exports = Promise;

function Promise(resolver) {
  if (typeof resolver !== 'function') {
    throw new TypeError('resolver must be a function');
  }
  this.state = PENDING;
  this.queue = [];
  this.outcome = void 0;
  if (resolver !== INTERNAL) {
    safelyResolveThenable(this, resolver);
  }
}

Promise.prototype["finally"] = function (callback) {
  if (typeof callback !== 'function') {
    return this;
  }
  var p = this.constructor;
  return this.then(resolve, reject);

  function resolve(value) {
    function yes () {
      return value;
    }
    return p.resolve(callback()).then(yes);
  }
  function reject(reason) {
    function no () {
      throw reason;
    }
    return p.resolve(callback()).then(no);
  }
};
Promise.prototype["catch"] = function (onRejected) {
  return this.then(null, onRejected);
};
Promise.prototype.then = function (onFulfilled, onRejected) {
  if (typeof onFulfilled !== 'function' && this.state === FULFILLED ||
    typeof onRejected !== 'function' && this.state === REJECTED) {
    return this;
  }
  var promise = new this.constructor(INTERNAL);
  if (this.state !== PENDING) {
    var resolver = this.state === FULFILLED ? onFulfilled : onRejected;
    unwrap(promise, resolver, this.outcome);
  } else {
    this.queue.push(new QueueItem(promise, onFulfilled, onRejected));
  }

  return promise;
};
function QueueItem(promise, onFulfilled, onRejected) {
  this.promise = promise;
  if (typeof onFulfilled === 'function') {
    this.onFulfilled = onFulfilled;
    this.callFulfilled = this.otherCallFulfilled;
  }
  if (typeof onRejected === 'function') {
    this.onRejected = onRejected;
    this.callRejected = this.otherCallRejected;
  }
}
QueueItem.prototype.callFulfilled = function (value) {
  handlers.resolve(this.promise, value);
};
QueueItem.prototype.otherCallFulfilled = function (value) {
  unwrap(this.promise, this.onFulfilled, value);
};
QueueItem.prototype.callRejected = function (value) {
  handlers.reject(this.promise, value);
};
QueueItem.prototype.otherCallRejected = function (value) {
  unwrap(this.promise, this.onRejected, value);
};

function unwrap(promise, func, value) {
  immediate(function () {
    var returnValue;
    try {
      returnValue = func(value);
    } catch (e) {
      return handlers.reject(promise, e);
    }
    if (returnValue === promise) {
      handlers.reject(promise, new TypeError('Cannot resolve promise with itself'));
    } else {
      handlers.resolve(promise, returnValue);
    }
  });
}

handlers.resolve = function (self, value) {
  var result = tryCatch(getThen, value);
  if (result.status === 'error') {
    return handlers.reject(self, result.value);
  }
  var thenable = result.value;

  if (thenable) {
    safelyResolveThenable(self, thenable);
  } else {
    self.state = FULFILLED;
    self.outcome = value;
    var i = -1;
    var len = self.queue.length;
    while (++i < len) {
      self.queue[i].callFulfilled(value);
    }
  }
  return self;
};
handlers.reject = function (self, error) {
  self.state = REJECTED;
  self.outcome = error;
  var i = -1;
  var len = self.queue.length;
  while (++i < len) {
    self.queue[i].callRejected(error);
  }
  return self;
};

function getThen(obj) {
  // Make sure we only access the accessor once as required by the spec
  var then = obj && obj.then;
  if (obj && (typeof obj === 'object' || typeof obj === 'function') && typeof then === 'function') {
    return function appyThen() {
      then.apply(obj, arguments);
    };
  }
}

function safelyResolveThenable(self, thenable) {
  // Either fulfill, reject or reject with error
  var called = false;
  function onError(value) {
    if (called) {
      return;
    }
    called = true;
    handlers.reject(self, value);
  }

  function onSuccess(value) {
    if (called) {
      return;
    }
    called = true;
    handlers.resolve(self, value);
  }

  function tryToUnwrap() {
    thenable(onSuccess, onError);
  }

  var result = tryCatch(tryToUnwrap);
  if (result.status === 'error') {
    onError(result.value);
  }
}

function tryCatch(func, value) {
  var out = {};
  try {
    out.value = func(value);
    out.status = 'success';
  } catch (e) {
    out.status = 'error';
    out.value = e;
  }
  return out;
}

Promise.resolve = resolve;
function resolve(value) {
  if (value instanceof this) {
    return value;
  }
  return handlers.resolve(new this(INTERNAL), value);
}

Promise.reject = reject;
function reject(reason) {
  var promise = new this(INTERNAL);
  return handlers.reject(promise, reason);
}

Promise.all = all;
function all(iterable) {
  var self = this;
  if (Object.prototype.toString.call(iterable) !== '[object Array]') {
    return this.reject(new TypeError('must be an array'));
  }

  var len = iterable.length;
  var called = false;
  if (!len) {
    return this.resolve([]);
  }

  var values = new Array(len);
  var resolved = 0;
  var i = -1;
  var promise = new this(INTERNAL);

  while (++i < len) {
    allResolver(iterable[i], i);
  }
  return promise;
  function allResolver(value, i) {
    self.resolve(value).then(resolveFromAll, function (error) {
      if (!called) {
        called = true;
        handlers.reject(promise, error);
      }
    });
    function resolveFromAll(outValue) {
      values[i] = outValue;
      if (++resolved === len && !called) {
        called = true;
        handlers.resolve(promise, values);
      }
    }
  }
}

Promise.race = race;
function race(iterable) {
  var self = this;
  if (Object.prototype.toString.call(iterable) !== '[object Array]') {
    return this.reject(new TypeError('must be an array'));
  }

  var len = iterable.length;
  var called = false;
  if (!len) {
    return this.resolve([]);
  }

  var i = -1;
  var promise = new this(INTERNAL);

  while (++i < len) {
    resolver(iterable[i]);
  }
  return promise;
  function resolver(value) {
    self.resolve(value).then(function (response) {
      if (!called) {
        called = true;
        handlers.resolve(promise, response);
      }
    }, function (error) {
      if (!called) {
        called = true;
        handlers.reject(promise, error);
      }
    });
  }
}

},{"immediate":36}],38:[function(require,module,exports){
// Top level file is just a mixin of submodules & constants
'use strict';

var assign    = require('./lib/utils/common').assign;

var deflate   = require('./lib/deflate');
var inflate   = require('./lib/inflate');
var constants = require('./lib/zlib/constants');

var pako = {};

assign(pako, deflate, inflate, constants);

module.exports = pako;

},{"./lib/deflate":39,"./lib/inflate":40,"./lib/utils/common":41,"./lib/zlib/constants":44}],39:[function(require,module,exports){
'use strict';


var zlib_deflate = require('./zlib/deflate');
var utils        = require('./utils/common');
var strings      = require('./utils/strings');
var msg          = require('./zlib/messages');
var ZStream      = require('./zlib/zstream');

var toString = Object.prototype.toString;

/* Public constants ==========================================================*/
/* ===========================================================================*/

var Z_NO_FLUSH      = 0;
var Z_FINISH        = 4;

var Z_OK            = 0;
var Z_STREAM_END    = 1;
var Z_SYNC_FLUSH    = 2;

var Z_DEFAULT_COMPRESSION = -1;

var Z_DEFAULT_STRATEGY    = 0;

var Z_DEFLATED  = 8;

/* ===========================================================================*/


/**
 * class Deflate
 *
 * Generic JS-style wrapper for zlib calls. If you don't need
 * streaming behaviour - use more simple functions: [[deflate]],
 * [[deflateRaw]] and [[gzip]].
 **/

/* internal
 * Deflate.chunks -> Array
 *
 * Chunks of output data, if [[Deflate#onData]] not overriden.
 **/

/**
 * Deflate.result -> Uint8Array|Array
 *
 * Compressed result, generated by default [[Deflate#onData]]
 * and [[Deflate#onEnd]] handlers. Filled after you push last chunk
 * (call [[Deflate#push]] with `Z_FINISH` / `true` param)  or if you
 * push a chunk with explicit flush (call [[Deflate#push]] with
 * `Z_SYNC_FLUSH` param).
 **/

/**
 * Deflate.err -> Number
 *
 * Error code after deflate finished. 0 (Z_OK) on success.
 * You will not need it in real life, because deflate errors
 * are possible only on wrong options or bad `onData` / `onEnd`
 * custom handlers.
 **/

/**
 * Deflate.msg -> String
 *
 * Error message, if [[Deflate.err]] != 0
 **/


/**
 * new Deflate(options)
 * - options (Object): zlib deflate options.
 *
 * Creates new deflator instance with specified params. Throws exception
 * on bad params. Supported options:
 *
 * - `level`
 * - `windowBits`
 * - `memLevel`
 * - `strategy`
 * - `dictionary`
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information on these.
 *
 * Additional options, for internal needs:
 *
 * - `chunkSize` - size of generated data chunks (16K by default)
 * - `raw` (Boolean) - do raw deflate
 * - `gzip` (Boolean) - create gzip wrapper
 * - `to` (String) - if equal to 'string', then result will be "binary string"
 *    (each char code [0..255])
 * - `header` (Object) - custom header for gzip
 *   - `text` (Boolean) - true if compressed data believed to be text
 *   - `time` (Number) - modification time, unix timestamp
 *   - `os` (Number) - operation system code
 *   - `extra` (Array) - array of bytes with extra data (max 65536)
 *   - `name` (String) - file name (binary string)
 *   - `comment` (String) - comment (binary string)
 *   - `hcrc` (Boolean) - true if header crc should be added
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , chunk1 = Uint8Array([1,2,3,4,5,6,7,8,9])
 *   , chunk2 = Uint8Array([10,11,12,13,14,15,16,17,18,19]);
 *
 * var deflate = new pako.Deflate({ level: 3});
 *
 * deflate.push(chunk1, false);
 * deflate.push(chunk2, true);  // true -> last chunk
 *
 * if (deflate.err) { throw new Error(deflate.err); }
 *
 * console.log(deflate.result);
 * ```
 **/
function Deflate(options) {
  if (!(this instanceof Deflate)) return new Deflate(options);

  this.options = utils.assign({
    level: Z_DEFAULT_COMPRESSION,
    method: Z_DEFLATED,
    chunkSize: 16384,
    windowBits: 15,
    memLevel: 8,
    strategy: Z_DEFAULT_STRATEGY,
    to: ''
  }, options || {});

  var opt = this.options;

  if (opt.raw && (opt.windowBits > 0)) {
    opt.windowBits = -opt.windowBits;
  }

  else if (opt.gzip && (opt.windowBits > 0) && (opt.windowBits < 16)) {
    opt.windowBits += 16;
  }

  this.err    = 0;      // error code, if happens (0 = Z_OK)
  this.msg    = '';     // error message
  this.ended  = false;  // used to avoid multiple onEnd() calls
  this.chunks = [];     // chunks of compressed data

  this.strm = new ZStream();
  this.strm.avail_out = 0;

  var status = zlib_deflate.deflateInit2(
    this.strm,
    opt.level,
    opt.method,
    opt.windowBits,
    opt.memLevel,
    opt.strategy
  );

  if (status !== Z_OK) {
    throw new Error(msg[status]);
  }

  if (opt.header) {
    zlib_deflate.deflateSetHeader(this.strm, opt.header);
  }

  if (opt.dictionary) {
    var dict;
    // Convert data if needed
    if (typeof opt.dictionary === 'string') {
      // If we need to compress text, change encoding to utf8.
      dict = strings.string2buf(opt.dictionary);
    } else if (toString.call(opt.dictionary) === '[object ArrayBuffer]') {
      dict = new Uint8Array(opt.dictionary);
    } else {
      dict = opt.dictionary;
    }

    status = zlib_deflate.deflateSetDictionary(this.strm, dict);

    if (status !== Z_OK) {
      throw new Error(msg[status]);
    }

    this._dict_set = true;
  }
}

/**
 * Deflate#push(data[, mode]) -> Boolean
 * - data (Uint8Array|Array|ArrayBuffer|String): input data. Strings will be
 *   converted to utf8 byte sequence.
 * - mode (Number|Boolean): 0..6 for corresponding Z_NO_FLUSH..Z_TREE modes.
 *   See constants. Skipped or `false` means Z_NO_FLUSH, `true` meansh Z_FINISH.
 *
 * Sends input data to deflate pipe, generating [[Deflate#onData]] calls with
 * new compressed chunks. Returns `true` on success. The last data block must have
 * mode Z_FINISH (or `true`). That will flush internal pending buffers and call
 * [[Deflate#onEnd]]. For interim explicit flushes (without ending the stream) you
 * can use mode Z_SYNC_FLUSH, keeping the compression context.
 *
 * On fail call [[Deflate#onEnd]] with error code and return false.
 *
 * We strongly recommend to use `Uint8Array` on input for best speed (output
 * array format is detected automatically). Also, don't skip last param and always
 * use the same type in your code (boolean or number). That will improve JS speed.
 *
 * For regular `Array`-s make sure all elements are [0..255].
 *
 * ##### Example
 *
 * ```javascript
 * push(chunk, false); // push one of data chunks
 * ...
 * push(chunk, true);  // push last chunk
 * ```
 **/
Deflate.prototype.push = function (data, mode) {
  var strm = this.strm;
  var chunkSize = this.options.chunkSize;
  var status, _mode;

  if (this.ended) { return false; }

  _mode = (mode === ~~mode) ? mode : ((mode === true) ? Z_FINISH : Z_NO_FLUSH);

  // Convert data if needed
  if (typeof data === 'string') {
    // If we need to compress text, change encoding to utf8.
    strm.input = strings.string2buf(data);
  } else if (toString.call(data) === '[object ArrayBuffer]') {
    strm.input = new Uint8Array(data);
  } else {
    strm.input = data;
  }

  strm.next_in = 0;
  strm.avail_in = strm.input.length;

  do {
    if (strm.avail_out === 0) {
      strm.output = new utils.Buf8(chunkSize);
      strm.next_out = 0;
      strm.avail_out = chunkSize;
    }
    status = zlib_deflate.deflate(strm, _mode);    /* no bad return value */

    if (status !== Z_STREAM_END && status !== Z_OK) {
      this.onEnd(status);
      this.ended = true;
      return false;
    }
    if (strm.avail_out === 0 || (strm.avail_in === 0 && (_mode === Z_FINISH || _mode === Z_SYNC_FLUSH))) {
      if (this.options.to === 'string') {
        this.onData(strings.buf2binstring(utils.shrinkBuf(strm.output, strm.next_out)));
      } else {
        this.onData(utils.shrinkBuf(strm.output, strm.next_out));
      }
    }
  } while ((strm.avail_in > 0 || strm.avail_out === 0) && status !== Z_STREAM_END);

  // Finalize on the last chunk.
  if (_mode === Z_FINISH) {
    status = zlib_deflate.deflateEnd(this.strm);
    this.onEnd(status);
    this.ended = true;
    return status === Z_OK;
  }

  // callback interim results if Z_SYNC_FLUSH.
  if (_mode === Z_SYNC_FLUSH) {
    this.onEnd(Z_OK);
    strm.avail_out = 0;
    return true;
  }

  return true;
};


/**
 * Deflate#onData(chunk) -> Void
 * - chunk (Uint8Array|Array|String): ouput data. Type of array depends
 *   on js engine support. When string output requested, each chunk
 *   will be string.
 *
 * By default, stores data blocks in `chunks[]` property and glue
 * those in `onEnd`. Override this handler, if you need another behaviour.
 **/
Deflate.prototype.onData = function (chunk) {
  this.chunks.push(chunk);
};


/**
 * Deflate#onEnd(status) -> Void
 * - status (Number): deflate status. 0 (Z_OK) on success,
 *   other if not.
 *
 * Called once after you tell deflate that the input stream is
 * complete (Z_FINISH) or should be flushed (Z_SYNC_FLUSH)
 * or if an error happened. By default - join collected chunks,
 * free memory and fill `results` / `err` properties.
 **/
Deflate.prototype.onEnd = function (status) {
  // On success - join
  if (status === Z_OK) {
    if (this.options.to === 'string') {
      this.result = this.chunks.join('');
    } else {
      this.result = utils.flattenChunks(this.chunks);
    }
  }
  this.chunks = [];
  this.err = status;
  this.msg = this.strm.msg;
};


/**
 * deflate(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to compress.
 * - options (Object): zlib deflate options.
 *
 * Compress `data` with deflate algorithm and `options`.
 *
 * Supported options are:
 *
 * - level
 * - windowBits
 * - memLevel
 * - strategy
 * - dictionary
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information on these.
 *
 * Sugar (options):
 *
 * - `raw` (Boolean) - say that we work with raw stream, if you don't wish to specify
 *   negative windowBits implicitly.
 * - `to` (String) - if equal to 'string', then result will be "binary string"
 *    (each char code [0..255])
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , data = Uint8Array([1,2,3,4,5,6,7,8,9]);
 *
 * console.log(pako.deflate(data));
 * ```
 **/
function deflate(input, options) {
  var deflator = new Deflate(options);

  deflator.push(input, true);

  // That will never happens, if you don't cheat with options :)
  if (deflator.err) { throw deflator.msg || msg[deflator.err]; }

  return deflator.result;
}


/**
 * deflateRaw(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to compress.
 * - options (Object): zlib deflate options.
 *
 * The same as [[deflate]], but creates raw data, without wrapper
 * (header and adler32 crc).
 **/
function deflateRaw(input, options) {
  options = options || {};
  options.raw = true;
  return deflate(input, options);
}


/**
 * gzip(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to compress.
 * - options (Object): zlib deflate options.
 *
 * The same as [[deflate]], but create gzip wrapper instead of
 * deflate one.
 **/
function gzip(input, options) {
  options = options || {};
  options.gzip = true;
  return deflate(input, options);
}


exports.Deflate = Deflate;
exports.deflate = deflate;
exports.deflateRaw = deflateRaw;
exports.gzip = gzip;

},{"./utils/common":41,"./utils/strings":42,"./zlib/deflate":46,"./zlib/messages":51,"./zlib/zstream":53}],40:[function(require,module,exports){
'use strict';


var zlib_inflate = require('./zlib/inflate');
var utils        = require('./utils/common');
var strings      = require('./utils/strings');
var c            = require('./zlib/constants');
var msg          = require('./zlib/messages');
var ZStream      = require('./zlib/zstream');
var GZheader     = require('./zlib/gzheader');

var toString = Object.prototype.toString;

/**
 * class Inflate
 *
 * Generic JS-style wrapper for zlib calls. If you don't need
 * streaming behaviour - use more simple functions: [[inflate]]
 * and [[inflateRaw]].
 **/

/* internal
 * inflate.chunks -> Array
 *
 * Chunks of output data, if [[Inflate#onData]] not overriden.
 **/

/**
 * Inflate.result -> Uint8Array|Array|String
 *
 * Uncompressed result, generated by default [[Inflate#onData]]
 * and [[Inflate#onEnd]] handlers. Filled after you push last chunk
 * (call [[Inflate#push]] with `Z_FINISH` / `true` param) or if you
 * push a chunk with explicit flush (call [[Inflate#push]] with
 * `Z_SYNC_FLUSH` param).
 **/

/**
 * Inflate.err -> Number
 *
 * Error code after inflate finished. 0 (Z_OK) on success.
 * Should be checked if broken data possible.
 **/

/**
 * Inflate.msg -> String
 *
 * Error message, if [[Inflate.err]] != 0
 **/


/**
 * new Inflate(options)
 * - options (Object): zlib inflate options.
 *
 * Creates new inflator instance with specified params. Throws exception
 * on bad params. Supported options:
 *
 * - `windowBits`
 * - `dictionary`
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information on these.
 *
 * Additional options, for internal needs:
 *
 * - `chunkSize` - size of generated data chunks (16K by default)
 * - `raw` (Boolean) - do raw inflate
 * - `to` (String) - if equal to 'string', then result will be converted
 *   from utf8 to utf16 (javascript) string. When string output requested,
 *   chunk length can differ from `chunkSize`, depending on content.
 *
 * By default, when no options set, autodetect deflate/gzip data format via
 * wrapper header.
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , chunk1 = Uint8Array([1,2,3,4,5,6,7,8,9])
 *   , chunk2 = Uint8Array([10,11,12,13,14,15,16,17,18,19]);
 *
 * var inflate = new pako.Inflate({ level: 3});
 *
 * inflate.push(chunk1, false);
 * inflate.push(chunk2, true);  // true -> last chunk
 *
 * if (inflate.err) { throw new Error(inflate.err); }
 *
 * console.log(inflate.result);
 * ```
 **/
function Inflate(options) {
  if (!(this instanceof Inflate)) return new Inflate(options);

  this.options = utils.assign({
    chunkSize: 16384,
    windowBits: 0,
    to: ''
  }, options || {});

  var opt = this.options;

  // Force window size for `raw` data, if not set directly,
  // because we have no header for autodetect.
  if (opt.raw && (opt.windowBits >= 0) && (opt.windowBits < 16)) {
    opt.windowBits = -opt.windowBits;
    if (opt.windowBits === 0) { opt.windowBits = -15; }
  }

  // If `windowBits` not defined (and mode not raw) - set autodetect flag for gzip/deflate
  if ((opt.windowBits >= 0) && (opt.windowBits < 16) &&
      !(options && options.windowBits)) {
    opt.windowBits += 32;
  }

  // Gzip header has no info about windows size, we can do autodetect only
  // for deflate. So, if window size not set, force it to max when gzip possible
  if ((opt.windowBits > 15) && (opt.windowBits < 48)) {
    // bit 3 (16) -> gzipped data
    // bit 4 (32) -> autodetect gzip/deflate
    if ((opt.windowBits & 15) === 0) {
      opt.windowBits |= 15;
    }
  }

  this.err    = 0;      // error code, if happens (0 = Z_OK)
  this.msg    = '';     // error message
  this.ended  = false;  // used to avoid multiple onEnd() calls
  this.chunks = [];     // chunks of compressed data

  this.strm   = new ZStream();
  this.strm.avail_out = 0;

  var status  = zlib_inflate.inflateInit2(
    this.strm,
    opt.windowBits
  );

  if (status !== c.Z_OK) {
    throw new Error(msg[status]);
  }

  this.header = new GZheader();

  zlib_inflate.inflateGetHeader(this.strm, this.header);
}

/**
 * Inflate#push(data[, mode]) -> Boolean
 * - data (Uint8Array|Array|ArrayBuffer|String): input data
 * - mode (Number|Boolean): 0..6 for corresponding Z_NO_FLUSH..Z_TREE modes.
 *   See constants. Skipped or `false` means Z_NO_FLUSH, `true` meansh Z_FINISH.
 *
 * Sends input data to inflate pipe, generating [[Inflate#onData]] calls with
 * new output chunks. Returns `true` on success. The last data block must have
 * mode Z_FINISH (or `true`). That will flush internal pending buffers and call
 * [[Inflate#onEnd]]. For interim explicit flushes (without ending the stream) you
 * can use mode Z_SYNC_FLUSH, keeping the decompression context.
 *
 * On fail call [[Inflate#onEnd]] with error code and return false.
 *
 * We strongly recommend to use `Uint8Array` on input for best speed (output
 * format is detected automatically). Also, don't skip last param and always
 * use the same type in your code (boolean or number). That will improve JS speed.
 *
 * For regular `Array`-s make sure all elements are [0..255].
 *
 * ##### Example
 *
 * ```javascript
 * push(chunk, false); // push one of data chunks
 * ...
 * push(chunk, true);  // push last chunk
 * ```
 **/
Inflate.prototype.push = function (data, mode) {
  var strm = this.strm;
  var chunkSize = this.options.chunkSize;
  var dictionary = this.options.dictionary;
  var status, _mode;
  var next_out_utf8, tail, utf8str;
  var dict;

  // Flag to properly process Z_BUF_ERROR on testing inflate call
  // when we check that all output data was flushed.
  var allowBufError = false;

  if (this.ended) { return false; }
  _mode = (mode === ~~mode) ? mode : ((mode === true) ? c.Z_FINISH : c.Z_NO_FLUSH);

  // Convert data if needed
  if (typeof data === 'string') {
    // Only binary strings can be decompressed on practice
    strm.input = strings.binstring2buf(data);
  } else if (toString.call(data) === '[object ArrayBuffer]') {
    strm.input = new Uint8Array(data);
  } else {
    strm.input = data;
  }

  strm.next_in = 0;
  strm.avail_in = strm.input.length;

  do {
    if (strm.avail_out === 0) {
      strm.output = new utils.Buf8(chunkSize);
      strm.next_out = 0;
      strm.avail_out = chunkSize;
    }

    status = zlib_inflate.inflate(strm, c.Z_NO_FLUSH);    /* no bad return value */

    if (status === c.Z_NEED_DICT && dictionary) {
      // Convert data if needed
      if (typeof dictionary === 'string') {
        dict = strings.string2buf(dictionary);
      } else if (toString.call(dictionary) === '[object ArrayBuffer]') {
        dict = new Uint8Array(dictionary);
      } else {
        dict = dictionary;
      }

      status = zlib_inflate.inflateSetDictionary(this.strm, dict);

    }

    if (status === c.Z_BUF_ERROR && allowBufError === true) {
      status = c.Z_OK;
      allowBufError = false;
    }

    if (status !== c.Z_STREAM_END && status !== c.Z_OK) {
      this.onEnd(status);
      this.ended = true;
      return false;
    }

    if (strm.next_out) {
      if (strm.avail_out === 0 || status === c.Z_STREAM_END || (strm.avail_in === 0 && (_mode === c.Z_FINISH || _mode === c.Z_SYNC_FLUSH))) {

        if (this.options.to === 'string') {

          next_out_utf8 = strings.utf8border(strm.output, strm.next_out);

          tail = strm.next_out - next_out_utf8;
          utf8str = strings.buf2string(strm.output, next_out_utf8);

          // move tail
          strm.next_out = tail;
          strm.avail_out = chunkSize - tail;
          if (tail) { utils.arraySet(strm.output, strm.output, next_out_utf8, tail, 0); }

          this.onData(utf8str);

        } else {
          this.onData(utils.shrinkBuf(strm.output, strm.next_out));
        }
      }
    }

    // When no more input data, we should check that internal inflate buffers
    // are flushed. The only way to do it when avail_out = 0 - run one more
    // inflate pass. But if output data not exists, inflate return Z_BUF_ERROR.
    // Here we set flag to process this error properly.
    //
    // NOTE. Deflate does not return error in this case and does not needs such
    // logic.
    if (strm.avail_in === 0 && strm.avail_out === 0) {
      allowBufError = true;
    }

  } while ((strm.avail_in > 0 || strm.avail_out === 0) && status !== c.Z_STREAM_END);

  if (status === c.Z_STREAM_END) {
    _mode = c.Z_FINISH;
  }

  // Finalize on the last chunk.
  if (_mode === c.Z_FINISH) {
    status = zlib_inflate.inflateEnd(this.strm);
    this.onEnd(status);
    this.ended = true;
    return status === c.Z_OK;
  }

  // callback interim results if Z_SYNC_FLUSH.
  if (_mode === c.Z_SYNC_FLUSH) {
    this.onEnd(c.Z_OK);
    strm.avail_out = 0;
    return true;
  }

  return true;
};


/**
 * Inflate#onData(chunk) -> Void
 * - chunk (Uint8Array|Array|String): ouput data. Type of array depends
 *   on js engine support. When string output requested, each chunk
 *   will be string.
 *
 * By default, stores data blocks in `chunks[]` property and glue
 * those in `onEnd`. Override this handler, if you need another behaviour.
 **/
Inflate.prototype.onData = function (chunk) {
  this.chunks.push(chunk);
};


/**
 * Inflate#onEnd(status) -> Void
 * - status (Number): inflate status. 0 (Z_OK) on success,
 *   other if not.
 *
 * Called either after you tell inflate that the input stream is
 * complete (Z_FINISH) or should be flushed (Z_SYNC_FLUSH)
 * or if an error happened. By default - join collected chunks,
 * free memory and fill `results` / `err` properties.
 **/
Inflate.prototype.onEnd = function (status) {
  // On success - join
  if (status === c.Z_OK) {
    if (this.options.to === 'string') {
      // Glue & convert here, until we teach pako to send
      // utf8 alligned strings to onData
      this.result = this.chunks.join('');
    } else {
      this.result = utils.flattenChunks(this.chunks);
    }
  }
  this.chunks = [];
  this.err = status;
  this.msg = this.strm.msg;
};


/**
 * inflate(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * Decompress `data` with inflate/ungzip and `options`. Autodetect
 * format via wrapper header by default. That's why we don't provide
 * separate `ungzip` method.
 *
 * Supported options are:
 *
 * - windowBits
 *
 * [http://zlib.net/manual.html#Advanced](http://zlib.net/manual.html#Advanced)
 * for more information.
 *
 * Sugar (options):
 *
 * - `raw` (Boolean) - say that we work with raw stream, if you don't wish to specify
 *   negative windowBits implicitly.
 * - `to` (String) - if equal to 'string', then result will be converted
 *   from utf8 to utf16 (javascript) string. When string output requested,
 *   chunk length can differ from `chunkSize`, depending on content.
 *
 *
 * ##### Example:
 *
 * ```javascript
 * var pako = require('pako')
 *   , input = pako.deflate([1,2,3,4,5,6,7,8,9])
 *   , output;
 *
 * try {
 *   output = pako.inflate(input);
 * } catch (err)
 *   console.log(err);
 * }
 * ```
 **/
function inflate(input, options) {
  var inflator = new Inflate(options);

  inflator.push(input, true);

  // That will never happens, if you don't cheat with options :)
  if (inflator.err) { throw inflator.msg || msg[inflator.err]; }

  return inflator.result;
}


/**
 * inflateRaw(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * The same as [[inflate]], but creates raw data, without wrapper
 * (header and adler32 crc).
 **/
function inflateRaw(input, options) {
  options = options || {};
  options.raw = true;
  return inflate(input, options);
}


/**
 * ungzip(data[, options]) -> Uint8Array|Array|String
 * - data (Uint8Array|Array|String): input data to decompress.
 * - options (Object): zlib inflate options.
 *
 * Just shortcut to [[inflate]], because it autodetects format
 * by header.content. Done for convenience.
 **/


exports.Inflate = Inflate;
exports.inflate = inflate;
exports.inflateRaw = inflateRaw;
exports.ungzip  = inflate;

},{"./utils/common":41,"./utils/strings":42,"./zlib/constants":44,"./zlib/gzheader":47,"./zlib/inflate":49,"./zlib/messages":51,"./zlib/zstream":53}],41:[function(require,module,exports){
'use strict';


var TYPED_OK =  (typeof Uint8Array !== 'undefined') &&
                (typeof Uint16Array !== 'undefined') &&
                (typeof Int32Array !== 'undefined');


exports.assign = function (obj /*from1, from2, from3, ...*/) {
  var sources = Array.prototype.slice.call(arguments, 1);
  while (sources.length) {
    var source = sources.shift();
    if (!source) { continue; }

    if (typeof source !== 'object') {
      throw new TypeError(source + 'must be non-object');
    }

    for (var p in source) {
      if (source.hasOwnProperty(p)) {
        obj[p] = source[p];
      }
    }
  }

  return obj;
};


// reduce buffer size, avoiding mem copy
exports.shrinkBuf = function (buf, size) {
  if (buf.length === size) { return buf; }
  if (buf.subarray) { return buf.subarray(0, size); }
  buf.length = size;
  return buf;
};


var fnTyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    if (src.subarray && dest.subarray) {
      dest.set(src.subarray(src_offs, src_offs + len), dest_offs);
      return;
    }
    // Fallback to ordinary array
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    var i, l, len, pos, chunk, result;

    // calculate data length
    len = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      len += chunks[i].length;
    }

    // join chunks
    result = new Uint8Array(len);
    pos = 0;
    for (i = 0, l = chunks.length; i < l; i++) {
      chunk = chunks[i];
      result.set(chunk, pos);
      pos += chunk.length;
    }

    return result;
  }
};

var fnUntyped = {
  arraySet: function (dest, src, src_offs, len, dest_offs) {
    for (var i = 0; i < len; i++) {
      dest[dest_offs + i] = src[src_offs + i];
    }
  },
  // Join array of chunks to single array.
  flattenChunks: function (chunks) {
    return [].concat.apply([], chunks);
  }
};


// Enable/Disable typed arrays use, for testing
//
exports.setTyped = function (on) {
  if (on) {
    exports.Buf8  = Uint8Array;
    exports.Buf16 = Uint16Array;
    exports.Buf32 = Int32Array;
    exports.assign(exports, fnTyped);
  } else {
    exports.Buf8  = Array;
    exports.Buf16 = Array;
    exports.Buf32 = Array;
    exports.assign(exports, fnUntyped);
  }
};

exports.setTyped(TYPED_OK);

},{}],42:[function(require,module,exports){
// String encode/decode helpers
'use strict';


var utils = require('./common');


// Quick check if we can use fast array to bin string conversion
//
// - apply(Array) can fail on Android 2.2
// - apply(Uint8Array) can fail on iOS 5.1 Safary
//
var STR_APPLY_OK = true;
var STR_APPLY_UIA_OK = true;

try { String.fromCharCode.apply(null, [ 0 ]); } catch (__) { STR_APPLY_OK = false; }
try { String.fromCharCode.apply(null, new Uint8Array(1)); } catch (__) { STR_APPLY_UIA_OK = false; }


// Table with utf8 lengths (calculated by first byte of sequence)
// Note, that 5 & 6-byte values and some 4-byte values can not be represented in JS,
// because max possible codepoint is 0x10ffff
var _utf8len = new utils.Buf8(256);
for (var q = 0; q < 256; q++) {
  _utf8len[q] = (q >= 252 ? 6 : q >= 248 ? 5 : q >= 240 ? 4 : q >= 224 ? 3 : q >= 192 ? 2 : 1);
}
_utf8len[254] = _utf8len[254] = 1; // Invalid sequence start


// convert string to array (typed, when possible)
exports.string2buf = function (str) {
  var buf, c, c2, m_pos, i, str_len = str.length, buf_len = 0;

  // count binary size
  for (m_pos = 0; m_pos < str_len; m_pos++) {
    c = str.charCodeAt(m_pos);
    if ((c & 0xfc00) === 0xd800 && (m_pos + 1 < str_len)) {
      c2 = str.charCodeAt(m_pos + 1);
      if ((c2 & 0xfc00) === 0xdc00) {
        c = 0x10000 + ((c - 0xd800) << 10) + (c2 - 0xdc00);
        m_pos++;
      }
    }
    buf_len += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
  }

  // allocate buffer
  buf = new utils.Buf8(buf_len);

  // convert
  for (i = 0, m_pos = 0; i < buf_len; m_pos++) {
    c = str.charCodeAt(m_pos);
    if ((c & 0xfc00) === 0xd800 && (m_pos + 1 < str_len)) {
      c2 = str.charCodeAt(m_pos + 1);
      if ((c2 & 0xfc00) === 0xdc00) {
        c = 0x10000 + ((c - 0xd800) << 10) + (c2 - 0xdc00);
        m_pos++;
      }
    }
    if (c < 0x80) {
      /* one byte */
      buf[i++] = c;
    } else if (c < 0x800) {
      /* two bytes */
      buf[i++] = 0xC0 | (c >>> 6);
      buf[i++] = 0x80 | (c & 0x3f);
    } else if (c < 0x10000) {
      /* three bytes */
      buf[i++] = 0xE0 | (c >>> 12);
      buf[i++] = 0x80 | (c >>> 6 & 0x3f);
      buf[i++] = 0x80 | (c & 0x3f);
    } else {
      /* four bytes */
      buf[i++] = 0xf0 | (c >>> 18);
      buf[i++] = 0x80 | (c >>> 12 & 0x3f);
      buf[i++] = 0x80 | (c >>> 6 & 0x3f);
      buf[i++] = 0x80 | (c & 0x3f);
    }
  }

  return buf;
};

// Helper (used in 2 places)
function buf2binstring(buf, len) {
  // use fallback for big arrays to avoid stack overflow
  if (len < 65537) {
    if ((buf.subarray && STR_APPLY_UIA_OK) || (!buf.subarray && STR_APPLY_OK)) {
      return String.fromCharCode.apply(null, utils.shrinkBuf(buf, len));
    }
  }

  var result = '';
  for (var i = 0; i < len; i++) {
    result += String.fromCharCode(buf[i]);
  }
  return result;
}


// Convert byte array to binary string
exports.buf2binstring = function (buf) {
  return buf2binstring(buf, buf.length);
};


// Convert binary string (typed, when possible)
exports.binstring2buf = function (str) {
  var buf = new utils.Buf8(str.length);
  for (var i = 0, len = buf.length; i < len; i++) {
    buf[i] = str.charCodeAt(i);
  }
  return buf;
};


// convert array to string
exports.buf2string = function (buf, max) {
  var i, out, c, c_len;
  var len = max || buf.length;

  // Reserve max possible length (2 words per char)
  // NB: by unknown reasons, Array is significantly faster for
  //     String.fromCharCode.apply than Uint16Array.
  var utf16buf = new Array(len * 2);

  for (out = 0, i = 0; i < len;) {
    c = buf[i++];
    // quick process ascii
    if (c < 0x80) { utf16buf[out++] = c; continue; }

    c_len = _utf8len[c];
    // skip 5 & 6 byte codes
    if (c_len > 4) { utf16buf[out++] = 0xfffd; i += c_len - 1; continue; }

    // apply mask on first byte
    c &= c_len === 2 ? 0x1f : c_len === 3 ? 0x0f : 0x07;
    // join the rest
    while (c_len > 1 && i < len) {
      c = (c << 6) | (buf[i++] & 0x3f);
      c_len--;
    }

    // terminated by end of string?
    if (c_len > 1) { utf16buf[out++] = 0xfffd; continue; }

    if (c < 0x10000) {
      utf16buf[out++] = c;
    } else {
      c -= 0x10000;
      utf16buf[out++] = 0xd800 | ((c >> 10) & 0x3ff);
      utf16buf[out++] = 0xdc00 | (c & 0x3ff);
    }
  }

  return buf2binstring(utf16buf, out);
};


// Calculate max possible position in utf8 buffer,
// that will not break sequence. If that's not possible
// - (very small limits) return max size as is.
//
// buf[] - utf8 bytes array
// max   - length limit (mandatory);
exports.utf8border = function (buf, max) {
  var pos;

  max = max || buf.length;
  if (max > buf.length) { max = buf.length; }

  // go back from last position, until start of sequence found
  pos = max - 1;
  while (pos >= 0 && (buf[pos] & 0xC0) === 0x80) { pos--; }

  // Fuckup - very small and broken sequence,
  // return max, because we should return something anyway.
  if (pos < 0) { return max; }

  // If we came to start of buffer - that means vuffer is too small,
  // return max too.
  if (pos === 0) { return max; }

  return (pos + _utf8len[buf[pos]] > max) ? pos : max;
};

},{"./common":41}],43:[function(require,module,exports){
'use strict';

// Note: adler32 takes 12% for level 0 and 2% for level 6.
// It doesn't worth to make additional optimizationa as in original.
// Small size is preferable.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function adler32(adler, buf, len, pos) {
  var s1 = (adler & 0xffff) |0,
      s2 = ((adler >>> 16) & 0xffff) |0,
      n = 0;

  while (len !== 0) {
    // Set limit ~ twice less than 5552, to keep
    // s2 in 31-bits, because we force signed ints.
    // in other case %= will fail.
    n = len > 2000 ? 2000 : len;
    len -= n;

    do {
      s1 = (s1 + buf[pos++]) |0;
      s2 = (s2 + s1) |0;
    } while (--n);

    s1 %= 65521;
    s2 %= 65521;
  }

  return (s1 | (s2 << 16)) |0;
}


module.exports = adler32;

},{}],44:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {

  /* Allowed flush values; see deflate() and inflate() below for details */
  Z_NO_FLUSH:         0,
  Z_PARTIAL_FLUSH:    1,
  Z_SYNC_FLUSH:       2,
  Z_FULL_FLUSH:       3,
  Z_FINISH:           4,
  Z_BLOCK:            5,
  Z_TREES:            6,

  /* Return codes for the compression/decompression functions. Negative values
  * are errors, positive values are used for special but normal events.
  */
  Z_OK:               0,
  Z_STREAM_END:       1,
  Z_NEED_DICT:        2,
  Z_ERRNO:           -1,
  Z_STREAM_ERROR:    -2,
  Z_DATA_ERROR:      -3,
  //Z_MEM_ERROR:     -4,
  Z_BUF_ERROR:       -5,
  //Z_VERSION_ERROR: -6,

  /* compression levels */
  Z_NO_COMPRESSION:         0,
  Z_BEST_SPEED:             1,
  Z_BEST_COMPRESSION:       9,
  Z_DEFAULT_COMPRESSION:   -1,


  Z_FILTERED:               1,
  Z_HUFFMAN_ONLY:           2,
  Z_RLE:                    3,
  Z_FIXED:                  4,
  Z_DEFAULT_STRATEGY:       0,

  /* Possible values of the data_type field (though see inflate()) */
  Z_BINARY:                 0,
  Z_TEXT:                   1,
  //Z_ASCII:                1, // = Z_TEXT (deprecated)
  Z_UNKNOWN:                2,

  /* The deflate compression method */
  Z_DEFLATED:               8
  //Z_NULL:                 null // Use -1 or null inline, depending on var type
};

},{}],45:[function(require,module,exports){
'use strict';

// Note: we can't get significant speed boost here.
// So write code to minimize size - no pregenerated tables
// and array tools dependencies.

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// Use ordinary array, since untyped makes no boost here
function makeTable() {
  var c, table = [];

  for (var n = 0; n < 256; n++) {
    c = n;
    for (var k = 0; k < 8; k++) {
      c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
    }
    table[n] = c;
  }

  return table;
}

// Create table on load. Just 255 signed longs. Not a problem.
var crcTable = makeTable();


function crc32(crc, buf, len, pos) {
  var t = crcTable,
      end = pos + len;

  crc ^= -1;

  for (var i = pos; i < end; i++) {
    crc = (crc >>> 8) ^ t[(crc ^ buf[i]) & 0xFF];
  }

  return (crc ^ (-1)); // >>> 0;
}


module.exports = crc32;

},{}],46:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils   = require('../utils/common');
var trees   = require('./trees');
var adler32 = require('./adler32');
var crc32   = require('./crc32');
var msg     = require('./messages');

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
var Z_NO_FLUSH      = 0;
var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
//var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
//var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
//var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;


/* compression levels */
//var Z_NO_COMPRESSION      = 0;
//var Z_BEST_SPEED          = 1;
//var Z_BEST_COMPRESSION    = 9;
var Z_DEFAULT_COMPRESSION = -1;


var Z_FILTERED            = 1;
var Z_HUFFMAN_ONLY        = 2;
var Z_RLE                 = 3;
var Z_FIXED               = 4;
var Z_DEFAULT_STRATEGY    = 0;

/* Possible values of the data_type field (though see inflate()) */
//var Z_BINARY              = 0;
//var Z_TEXT                = 1;
//var Z_ASCII               = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;


/* The deflate compression method */
var Z_DEFLATED  = 8;

/*============================================================================*/


var MAX_MEM_LEVEL = 9;
/* Maximum value for memLevel in deflateInit2 */
var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_MEM_LEVEL = 8;


var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */
var LITERALS      = 256;
/* number of literal bytes 0..255 */
var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */
var D_CODES       = 30;
/* number of distance codes */
var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */
var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */
var MAX_BITS  = 15;
/* All codes must not exceed MAX_BITS bits */

var MIN_MATCH = 3;
var MAX_MATCH = 258;
var MIN_LOOKAHEAD = (MAX_MATCH + MIN_MATCH + 1);

var PRESET_DICT = 0x20;

var INIT_STATE = 42;
var EXTRA_STATE = 69;
var NAME_STATE = 73;
var COMMENT_STATE = 91;
var HCRC_STATE = 103;
var BUSY_STATE = 113;
var FINISH_STATE = 666;

var BS_NEED_MORE      = 1; /* block not completed, need more input or more output */
var BS_BLOCK_DONE     = 2; /* block flush performed */
var BS_FINISH_STARTED = 3; /* finish started, need only more output at next deflate */
var BS_FINISH_DONE    = 4; /* finish done, accept no more input or output */

var OS_CODE = 0x03; // Unix :) . Don't detect, use this default.

function err(strm, errorCode) {
  strm.msg = msg[errorCode];
  return errorCode;
}

function rank(f) {
  return ((f) << 1) - ((f) > 4 ? 9 : 0);
}

function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }


/* =========================================================================
 * Flush as much pending output as possible. All deflate() output goes
 * through this function so some applications may wish to modify it
 * to avoid allocating a large strm->output buffer and copying into it.
 * (See also read_buf()).
 */
function flush_pending(strm) {
  var s = strm.state;

  //_tr_flush_bits(s);
  var len = s.pending;
  if (len > strm.avail_out) {
    len = strm.avail_out;
  }
  if (len === 0) { return; }

  utils.arraySet(strm.output, s.pending_buf, s.pending_out, len, strm.next_out);
  strm.next_out += len;
  s.pending_out += len;
  strm.total_out += len;
  strm.avail_out -= len;
  s.pending -= len;
  if (s.pending === 0) {
    s.pending_out = 0;
  }
}


function flush_block_only(s, last) {
  trees._tr_flush_block(s, (s.block_start >= 0 ? s.block_start : -1), s.strstart - s.block_start, last);
  s.block_start = s.strstart;
  flush_pending(s.strm);
}


function put_byte(s, b) {
  s.pending_buf[s.pending++] = b;
}


/* =========================================================================
 * Put a short in the pending buffer. The 16-bit value is put in MSB order.
 * IN assertion: the stream state is correct and there is enough room in
 * pending_buf.
 */
function putShortMSB(s, b) {
//  put_byte(s, (Byte)(b >> 8));
//  put_byte(s, (Byte)(b & 0xff));
  s.pending_buf[s.pending++] = (b >>> 8) & 0xff;
  s.pending_buf[s.pending++] = b & 0xff;
}


/* ===========================================================================
 * Read a new buffer from the current input stream, update the adler32
 * and total number of bytes read.  All deflate() input goes through
 * this function so some applications may wish to modify it to avoid
 * allocating a large strm->input buffer and copying from it.
 * (See also flush_pending()).
 */
function read_buf(strm, buf, start, size) {
  var len = strm.avail_in;

  if (len > size) { len = size; }
  if (len === 0) { return 0; }

  strm.avail_in -= len;

  // zmemcpy(buf, strm->next_in, len);
  utils.arraySet(buf, strm.input, strm.next_in, len, start);
  if (strm.state.wrap === 1) {
    strm.adler = adler32(strm.adler, buf, len, start);
  }

  else if (strm.state.wrap === 2) {
    strm.adler = crc32(strm.adler, buf, len, start);
  }

  strm.next_in += len;
  strm.total_in += len;

  return len;
}


/* ===========================================================================
 * Set match_start to the longest match starting at the given string and
 * return its length. Matches shorter or equal to prev_length are discarded,
 * in which case the result is equal to prev_length and match_start is
 * garbage.
 * IN assertions: cur_match is the head of the hash chain for the current
 *   string (strstart) and its distance is <= MAX_DIST, and prev_length >= 1
 * OUT assertion: the match length is not greater than s->lookahead.
 */
function longest_match(s, cur_match) {
  var chain_length = s.max_chain_length;      /* max hash chain length */
  var scan = s.strstart; /* current string */
  var match;                       /* matched string */
  var len;                           /* length of current match */
  var best_len = s.prev_length;              /* best match length so far */
  var nice_match = s.nice_match;             /* stop if match long enough */
  var limit = (s.strstart > (s.w_size - MIN_LOOKAHEAD)) ?
      s.strstart - (s.w_size - MIN_LOOKAHEAD) : 0/*NIL*/;

  var _win = s.window; // shortcut

  var wmask = s.w_mask;
  var prev  = s.prev;

  /* Stop when cur_match becomes <= limit. To simplify the code,
   * we prevent matches with the string of window index 0.
   */

  var strend = s.strstart + MAX_MATCH;
  var scan_end1  = _win[scan + best_len - 1];
  var scan_end   = _win[scan + best_len];

  /* The code is optimized for HASH_BITS >= 8 and MAX_MATCH-2 multiple of 16.
   * It is easy to get rid of this optimization if necessary.
   */
  // Assert(s->hash_bits >= 8 && MAX_MATCH == 258, "Code too clever");

  /* Do not waste too much time if we already have a good match: */
  if (s.prev_length >= s.good_match) {
    chain_length >>= 2;
  }
  /* Do not look for matches beyond the end of the input. This is necessary
   * to make deflate deterministic.
   */
  if (nice_match > s.lookahead) { nice_match = s.lookahead; }

  // Assert((ulg)s->strstart <= s->window_size-MIN_LOOKAHEAD, "need lookahead");

  do {
    // Assert(cur_match < s->strstart, "no future");
    match = cur_match;

    /* Skip to next match if the match length cannot increase
     * or if the match length is less than 2.  Note that the checks below
     * for insufficient lookahead only occur occasionally for performance
     * reasons.  Therefore uninitialized memory will be accessed, and
     * conditional jumps will be made that depend on those values.
     * However the length of the match is limited to the lookahead, so
     * the output of deflate is not affected by the uninitialized values.
     */

    if (_win[match + best_len]     !== scan_end  ||
        _win[match + best_len - 1] !== scan_end1 ||
        _win[match]                !== _win[scan] ||
        _win[++match]              !== _win[scan + 1]) {
      continue;
    }

    /* The check at best_len-1 can be removed because it will be made
     * again later. (This heuristic is not always a win.)
     * It is not necessary to compare scan[2] and match[2] since they
     * are always equal when the other bytes match, given that
     * the hash keys are equal and that HASH_BITS >= 8.
     */
    scan += 2;
    match++;
    // Assert(*scan == *match, "match[2]?");

    /* We check for insufficient lookahead only every 8th comparison;
     * the 256th check will be made at strstart+258.
     */
    do {
      /*jshint noempty:false*/
    } while (_win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             _win[++scan] === _win[++match] && _win[++scan] === _win[++match] &&
             scan < strend);

    // Assert(scan <= s->window+(unsigned)(s->window_size-1), "wild scan");

    len = MAX_MATCH - (strend - scan);
    scan = strend - MAX_MATCH;

    if (len > best_len) {
      s.match_start = cur_match;
      best_len = len;
      if (len >= nice_match) {
        break;
      }
      scan_end1  = _win[scan + best_len - 1];
      scan_end   = _win[scan + best_len];
    }
  } while ((cur_match = prev[cur_match & wmask]) > limit && --chain_length !== 0);

  if (best_len <= s.lookahead) {
    return best_len;
  }
  return s.lookahead;
}


/* ===========================================================================
 * Fill the window when the lookahead becomes insufficient.
 * Updates strstart and lookahead.
 *
 * IN assertion: lookahead < MIN_LOOKAHEAD
 * OUT assertions: strstart <= window_size-MIN_LOOKAHEAD
 *    At least one byte has been read, or avail_in == 0; reads are
 *    performed for at least two bytes (required for the zip translate_eol
 *    option -- not supported here).
 */
function fill_window(s) {
  var _w_size = s.w_size;
  var p, n, m, more, str;

  //Assert(s->lookahead < MIN_LOOKAHEAD, "already enough lookahead");

  do {
    more = s.window_size - s.lookahead - s.strstart;

    // JS ints have 32 bit, block below not needed
    /* Deal with !@#$% 64K limit: */
    //if (sizeof(int) <= 2) {
    //    if (more == 0 && s->strstart == 0 && s->lookahead == 0) {
    //        more = wsize;
    //
    //  } else if (more == (unsigned)(-1)) {
    //        /* Very unlikely, but possible on 16 bit machine if
    //         * strstart == 0 && lookahead == 1 (input done a byte at time)
    //         */
    //        more--;
    //    }
    //}


    /* If the window is almost full and there is insufficient lookahead,
     * move the upper half to the lower one to make room in the upper half.
     */
    if (s.strstart >= _w_size + (_w_size - MIN_LOOKAHEAD)) {

      utils.arraySet(s.window, s.window, _w_size, _w_size, 0);
      s.match_start -= _w_size;
      s.strstart -= _w_size;
      /* we now have strstart >= MAX_DIST */
      s.block_start -= _w_size;

      /* Slide the hash table (could be avoided with 32 bit values
       at the expense of memory usage). We slide even when level == 0
       to keep the hash table consistent if we switch back to level > 0
       later. (Using level 0 permanently is not an optimal usage of
       zlib, so we don't care about this pathological case.)
       */

      n = s.hash_size;
      p = n;
      do {
        m = s.head[--p];
        s.head[p] = (m >= _w_size ? m - _w_size : 0);
      } while (--n);

      n = _w_size;
      p = n;
      do {
        m = s.prev[--p];
        s.prev[p] = (m >= _w_size ? m - _w_size : 0);
        /* If n is not on any hash chain, prev[n] is garbage but
         * its value will never be used.
         */
      } while (--n);

      more += _w_size;
    }
    if (s.strm.avail_in === 0) {
      break;
    }

    /* If there was no sliding:
     *    strstart <= WSIZE+MAX_DIST-1 && lookahead <= MIN_LOOKAHEAD - 1 &&
     *    more == window_size - lookahead - strstart
     * => more >= window_size - (MIN_LOOKAHEAD-1 + WSIZE + MAX_DIST-1)
     * => more >= window_size - 2*WSIZE + 2
     * In the BIG_MEM or MMAP case (not yet supported),
     *   window_size == input_size + MIN_LOOKAHEAD  &&
     *   strstart + s->lookahead <= input_size => more >= MIN_LOOKAHEAD.
     * Otherwise, window_size == 2*WSIZE so more >= 2.
     * If there was sliding, more >= WSIZE. So in all cases, more >= 2.
     */
    //Assert(more >= 2, "more < 2");
    n = read_buf(s.strm, s.window, s.strstart + s.lookahead, more);
    s.lookahead += n;

    /* Initialize the hash value now that we have some input: */
    if (s.lookahead + s.insert >= MIN_MATCH) {
      str = s.strstart - s.insert;
      s.ins_h = s.window[str];

      /* UPDATE_HASH(s, s->ins_h, s->window[str + 1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + 1]) & s.hash_mask;
//#if MIN_MATCH != 3
//        Call update_hash() MIN_MATCH-3 more times
//#endif
      while (s.insert) {
        /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

        s.prev[str & s.w_mask] = s.head[s.ins_h];
        s.head[s.ins_h] = str;
        str++;
        s.insert--;
        if (s.lookahead + s.insert < MIN_MATCH) {
          break;
        }
      }
    }
    /* If the whole input has less than MIN_MATCH bytes, ins_h is garbage,
     * but this is not important since only literal bytes will be emitted.
     */

  } while (s.lookahead < MIN_LOOKAHEAD && s.strm.avail_in !== 0);

  /* If the WIN_INIT bytes after the end of the current data have never been
   * written, then zero those bytes in order to avoid memory check reports of
   * the use of uninitialized (or uninitialised as Julian writes) bytes by
   * the longest match routines.  Update the high water mark for the next
   * time through here.  WIN_INIT is set to MAX_MATCH since the longest match
   * routines allow scanning to strstart + MAX_MATCH, ignoring lookahead.
   */
//  if (s.high_water < s.window_size) {
//    var curr = s.strstart + s.lookahead;
//    var init = 0;
//
//    if (s.high_water < curr) {
//      /* Previous high water mark below current data -- zero WIN_INIT
//       * bytes or up to end of window, whichever is less.
//       */
//      init = s.window_size - curr;
//      if (init > WIN_INIT)
//        init = WIN_INIT;
//      zmemzero(s->window + curr, (unsigned)init);
//      s->high_water = curr + init;
//    }
//    else if (s->high_water < (ulg)curr + WIN_INIT) {
//      /* High water mark at or above current data, but below current data
//       * plus WIN_INIT -- zero out to current data plus WIN_INIT, or up
//       * to end of window, whichever is less.
//       */
//      init = (ulg)curr + WIN_INIT - s->high_water;
//      if (init > s->window_size - s->high_water)
//        init = s->window_size - s->high_water;
//      zmemzero(s->window + s->high_water, (unsigned)init);
//      s->high_water += init;
//    }
//  }
//
//  Assert((ulg)s->strstart <= s->window_size - MIN_LOOKAHEAD,
//    "not enough room for search");
}

/* ===========================================================================
 * Copy without compression as much as possible from the input stream, return
 * the current block state.
 * This function does not insert new strings in the dictionary since
 * uncompressible data is probably not useful. This function is used
 * only for the level=0 compression option.
 * NOTE: this function should be optimized to avoid extra copying from
 * window to pending_buf.
 */
function deflate_stored(s, flush) {
  /* Stored blocks are limited to 0xffff bytes, pending_buf is limited
   * to pending_buf_size, and each stored block has a 5 byte header:
   */
  var max_block_size = 0xffff;

  if (max_block_size > s.pending_buf_size - 5) {
    max_block_size = s.pending_buf_size - 5;
  }

  /* Copy as much as possible from input to output: */
  for (;;) {
    /* Fill the window as much as possible: */
    if (s.lookahead <= 1) {

      //Assert(s->strstart < s->w_size+MAX_DIST(s) ||
      //  s->block_start >= (long)s->w_size, "slide too late");
//      if (!(s.strstart < s.w_size + (s.w_size - MIN_LOOKAHEAD) ||
//        s.block_start >= s.w_size)) {
//        throw  new Error("slide too late");
//      }

      fill_window(s);
      if (s.lookahead === 0 && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }

      if (s.lookahead === 0) {
        break;
      }
      /* flush the current block */
    }
    //Assert(s->block_start >= 0L, "block gone");
//    if (s.block_start < 0) throw new Error("block gone");

    s.strstart += s.lookahead;
    s.lookahead = 0;

    /* Emit a stored block if pending_buf will be full: */
    var max_start = s.block_start + max_block_size;

    if (s.strstart === 0 || s.strstart >= max_start) {
      /* strstart == 0 is possible when wraparound on 16-bit machine */
      s.lookahead = s.strstart - max_start;
      s.strstart = max_start;
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/


    }
    /* Flush if we may have to slide, otherwise block_start may become
     * negative and the data will be gone:
     */
    if (s.strstart - s.block_start >= (s.w_size - MIN_LOOKAHEAD)) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }

  s.insert = 0;

  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }

  if (s.strstart > s.block_start) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_NEED_MORE;
}

/* ===========================================================================
 * Compress as much as possible from the input stream, return the current
 * block state.
 * This function does not perform lazy evaluation of matches and inserts
 * new strings in the dictionary only for unmatched strings or for short
 * matches. It is used only for the fast compression options.
 */
function deflate_fast(s, flush) {
  var hash_head;        /* head of the hash chain */
  var bflush;           /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) {
        break; /* flush the current block */
      }
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     * At this point we have always match_length < MIN_MATCH
     */
    if (hash_head !== 0/*NIL*/ && ((s.strstart - hash_head) <= (s.w_size - MIN_LOOKAHEAD))) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */
    }
    if (s.match_length >= MIN_MATCH) {
      // check_match(s, s.strstart, s.match_start, s.match_length); // for debug only

      /*** _tr_tally_dist(s, s.strstart - s.match_start,
                     s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, s.strstart - s.match_start, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;

      /* Insert new strings in the hash table only if the match length
       * is not too large. This saves time but degrades compression.
       */
      if (s.match_length <= s.max_lazy_match/*max_insert_length*/ && s.lookahead >= MIN_MATCH) {
        s.match_length--; /* string at strstart already in table */
        do {
          s.strstart++;
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
          /* strstart never exceeds WSIZE-MAX_MATCH, so there are
           * always MIN_MATCH bytes ahead.
           */
        } while (--s.match_length !== 0);
        s.strstart++;
      } else
      {
        s.strstart += s.match_length;
        s.match_length = 0;
        s.ins_h = s.window[s.strstart];
        /* UPDATE_HASH(s, s.ins_h, s.window[s.strstart+1]); */
        s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + 1]) & s.hash_mask;

//#if MIN_MATCH != 3
//                Call UPDATE_HASH() MIN_MATCH-3 more times
//#endif
        /* If lookahead < MIN_MATCH, ins_h is garbage, but it does not
         * matter since it will be recomputed at next deflate call.
         */
      }
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s.window[s.strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = ((s.strstart < (MIN_MATCH - 1)) ? s.strstart : MIN_MATCH - 1);
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * Same as above, but achieves better compression. We use a lazy
 * evaluation for matches: a match is finally adopted only if there is
 * no better match at the next window position.
 */
function deflate_slow(s, flush) {
  var hash_head;          /* head of hash chain */
  var bflush;              /* set if current block must be flushed */

  var max_insert;

  /* Process the input block. */
  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the next match, plus MIN_MATCH bytes to insert the
     * string following the next match.
     */
    if (s.lookahead < MIN_LOOKAHEAD) {
      fill_window(s);
      if (s.lookahead < MIN_LOOKAHEAD && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* Insert the string window[strstart .. strstart+2] in the
     * dictionary, and set hash_head to the head of the hash chain:
     */
    hash_head = 0/*NIL*/;
    if (s.lookahead >= MIN_MATCH) {
      /*** INSERT_STRING(s, s.strstart, hash_head); ***/
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
      hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
      s.head[s.ins_h] = s.strstart;
      /***/
    }

    /* Find the longest match, discarding those <= prev_length.
     */
    s.prev_length = s.match_length;
    s.prev_match = s.match_start;
    s.match_length = MIN_MATCH - 1;

    if (hash_head !== 0/*NIL*/ && s.prev_length < s.max_lazy_match &&
        s.strstart - hash_head <= (s.w_size - MIN_LOOKAHEAD)/*MAX_DIST(s)*/) {
      /* To simplify the code, we prevent matches with the string
       * of window index 0 (in particular we have to avoid a match
       * of the string with itself at the start of the input file).
       */
      s.match_length = longest_match(s, hash_head);
      /* longest_match() sets match_start */

      if (s.match_length <= 5 &&
         (s.strategy === Z_FILTERED || (s.match_length === MIN_MATCH && s.strstart - s.match_start > 4096/*TOO_FAR*/))) {

        /* If prev_match is also MIN_MATCH, match_start is garbage
         * but we will ignore the current match anyway.
         */
        s.match_length = MIN_MATCH - 1;
      }
    }
    /* If there was a match at the previous step and the current
     * match is not better, output the previous match:
     */
    if (s.prev_length >= MIN_MATCH && s.match_length <= s.prev_length) {
      max_insert = s.strstart + s.lookahead - MIN_MATCH;
      /* Do not insert strings in hash table beyond this. */

      //check_match(s, s.strstart-1, s.prev_match, s.prev_length);

      /***_tr_tally_dist(s, s.strstart - 1 - s.prev_match,
                     s.prev_length - MIN_MATCH, bflush);***/
      bflush = trees._tr_tally(s, s.strstart - 1 - s.prev_match, s.prev_length - MIN_MATCH);
      /* Insert in hash table all strings up to the end of the match.
       * strstart-1 and strstart are already inserted. If there is not
       * enough lookahead, the last two strings are not inserted in
       * the hash table.
       */
      s.lookahead -= s.prev_length - 1;
      s.prev_length -= 2;
      do {
        if (++s.strstart <= max_insert) {
          /*** INSERT_STRING(s, s.strstart, hash_head); ***/
          s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[s.strstart + MIN_MATCH - 1]) & s.hash_mask;
          hash_head = s.prev[s.strstart & s.w_mask] = s.head[s.ins_h];
          s.head[s.ins_h] = s.strstart;
          /***/
        }
      } while (--s.prev_length !== 0);
      s.match_available = 0;
      s.match_length = MIN_MATCH - 1;
      s.strstart++;

      if (bflush) {
        /*** FLUSH_BLOCK(s, 0); ***/
        flush_block_only(s, false);
        if (s.strm.avail_out === 0) {
          return BS_NEED_MORE;
        }
        /***/
      }

    } else if (s.match_available) {
      /* If there was no match at the previous position, output a
       * single literal. If there was a match but the current match
       * is longer, truncate the previous match to a single literal.
       */
      //Tracevv((stderr,"%c", s->window[s->strstart-1]));
      /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

      if (bflush) {
        /*** FLUSH_BLOCK_ONLY(s, 0) ***/
        flush_block_only(s, false);
        /***/
      }
      s.strstart++;
      s.lookahead--;
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
    } else {
      /* There is no previous match to compare with, wait for
       * the next step to decide.
       */
      s.match_available = 1;
      s.strstart++;
      s.lookahead--;
    }
  }
  //Assert (flush != Z_NO_FLUSH, "no flush?");
  if (s.match_available) {
    //Tracevv((stderr,"%c", s->window[s->strstart-1]));
    /*** _tr_tally_lit(s, s.window[s.strstart-1], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart - 1]);

    s.match_available = 0;
  }
  s.insert = s.strstart < MIN_MATCH - 1 ? s.strstart : MIN_MATCH - 1;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }

  return BS_BLOCK_DONE;
}


/* ===========================================================================
 * For Z_RLE, simply look for runs of bytes, generate matches only of distance
 * one.  Do not maintain a hash table.  (It will be regenerated if this run of
 * deflate switches away from Z_RLE.)
 */
function deflate_rle(s, flush) {
  var bflush;            /* set if current block must be flushed */
  var prev;              /* byte at distance one to match */
  var scan, strend;      /* scan goes up to strend for length of run */

  var _win = s.window;

  for (;;) {
    /* Make sure that we always have enough lookahead, except
     * at the end of the input file. We need MAX_MATCH bytes
     * for the longest run, plus one for the unrolled loop.
     */
    if (s.lookahead <= MAX_MATCH) {
      fill_window(s);
      if (s.lookahead <= MAX_MATCH && flush === Z_NO_FLUSH) {
        return BS_NEED_MORE;
      }
      if (s.lookahead === 0) { break; } /* flush the current block */
    }

    /* See how many times the previous byte repeats */
    s.match_length = 0;
    if (s.lookahead >= MIN_MATCH && s.strstart > 0) {
      scan = s.strstart - 1;
      prev = _win[scan];
      if (prev === _win[++scan] && prev === _win[++scan] && prev === _win[++scan]) {
        strend = s.strstart + MAX_MATCH;
        do {
          /*jshint noempty:false*/
        } while (prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 prev === _win[++scan] && prev === _win[++scan] &&
                 scan < strend);
        s.match_length = MAX_MATCH - (strend - scan);
        if (s.match_length > s.lookahead) {
          s.match_length = s.lookahead;
        }
      }
      //Assert(scan <= s->window+(uInt)(s->window_size-1), "wild scan");
    }

    /* Emit match if have run of MIN_MATCH or longer, else emit literal */
    if (s.match_length >= MIN_MATCH) {
      //check_match(s, s.strstart, s.strstart - 1, s.match_length);

      /*** _tr_tally_dist(s, 1, s.match_length - MIN_MATCH, bflush); ***/
      bflush = trees._tr_tally(s, 1, s.match_length - MIN_MATCH);

      s.lookahead -= s.match_length;
      s.strstart += s.match_length;
      s.match_length = 0;
    } else {
      /* No match, output a literal byte */
      //Tracevv((stderr,"%c", s->window[s->strstart]));
      /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
      bflush = trees._tr_tally(s, 0, s.window[s.strstart]);

      s.lookahead--;
      s.strstart++;
    }
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* ===========================================================================
 * For Z_HUFFMAN_ONLY, do not look for matches.  Do not maintain a hash table.
 * (It will be regenerated if this run of deflate switches away from Huffman.)
 */
function deflate_huff(s, flush) {
  var bflush;             /* set if current block must be flushed */

  for (;;) {
    /* Make sure that we have a literal to write. */
    if (s.lookahead === 0) {
      fill_window(s);
      if (s.lookahead === 0) {
        if (flush === Z_NO_FLUSH) {
          return BS_NEED_MORE;
        }
        break;      /* flush the current block */
      }
    }

    /* Output a literal byte */
    s.match_length = 0;
    //Tracevv((stderr,"%c", s->window[s->strstart]));
    /*** _tr_tally_lit(s, s.window[s.strstart], bflush); ***/
    bflush = trees._tr_tally(s, 0, s.window[s.strstart]);
    s.lookahead--;
    s.strstart++;
    if (bflush) {
      /*** FLUSH_BLOCK(s, 0); ***/
      flush_block_only(s, false);
      if (s.strm.avail_out === 0) {
        return BS_NEED_MORE;
      }
      /***/
    }
  }
  s.insert = 0;
  if (flush === Z_FINISH) {
    /*** FLUSH_BLOCK(s, 1); ***/
    flush_block_only(s, true);
    if (s.strm.avail_out === 0) {
      return BS_FINISH_STARTED;
    }
    /***/
    return BS_FINISH_DONE;
  }
  if (s.last_lit) {
    /*** FLUSH_BLOCK(s, 0); ***/
    flush_block_only(s, false);
    if (s.strm.avail_out === 0) {
      return BS_NEED_MORE;
    }
    /***/
  }
  return BS_BLOCK_DONE;
}

/* Values for max_lazy_match, good_match and max_chain_length, depending on
 * the desired pack level (0..9). The values given below have been tuned to
 * exclude worst case performance for pathological files. Better values may be
 * found for specific files.
 */
function Config(good_length, max_lazy, nice_length, max_chain, func) {
  this.good_length = good_length;
  this.max_lazy = max_lazy;
  this.nice_length = nice_length;
  this.max_chain = max_chain;
  this.func = func;
}

var configuration_table;

configuration_table = [
  /*      good lazy nice chain */
  new Config(0, 0, 0, 0, deflate_stored),          /* 0 store only */
  new Config(4, 4, 8, 4, deflate_fast),            /* 1 max speed, no lazy matches */
  new Config(4, 5, 16, 8, deflate_fast),           /* 2 */
  new Config(4, 6, 32, 32, deflate_fast),          /* 3 */

  new Config(4, 4, 16, 16, deflate_slow),          /* 4 lazy matches */
  new Config(8, 16, 32, 32, deflate_slow),         /* 5 */
  new Config(8, 16, 128, 128, deflate_slow),       /* 6 */
  new Config(8, 32, 128, 256, deflate_slow),       /* 7 */
  new Config(32, 128, 258, 1024, deflate_slow),    /* 8 */
  new Config(32, 258, 258, 4096, deflate_slow)     /* 9 max compression */
];


/* ===========================================================================
 * Initialize the "longest match" routines for a new zlib stream
 */
function lm_init(s) {
  s.window_size = 2 * s.w_size;

  /*** CLEAR_HASH(s); ***/
  zero(s.head); // Fill with NIL (= 0);

  /* Set the default configuration parameters:
   */
  s.max_lazy_match = configuration_table[s.level].max_lazy;
  s.good_match = configuration_table[s.level].good_length;
  s.nice_match = configuration_table[s.level].nice_length;
  s.max_chain_length = configuration_table[s.level].max_chain;

  s.strstart = 0;
  s.block_start = 0;
  s.lookahead = 0;
  s.insert = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  s.ins_h = 0;
}


function DeflateState() {
  this.strm = null;            /* pointer back to this zlib stream */
  this.status = 0;            /* as the name implies */
  this.pending_buf = null;      /* output still pending */
  this.pending_buf_size = 0;  /* size of pending_buf */
  this.pending_out = 0;       /* next pending byte to output to the stream */
  this.pending = 0;           /* nb of bytes in the pending buffer */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.gzhead = null;         /* gzip header information to write */
  this.gzindex = 0;           /* where in extra, name, or comment */
  this.method = Z_DEFLATED; /* can only be DEFLATED */
  this.last_flush = -1;   /* value of flush param for previous deflate call */

  this.w_size = 0;  /* LZ77 window size (32K by default) */
  this.w_bits = 0;  /* log2(w_size)  (8..16) */
  this.w_mask = 0;  /* w_size - 1 */

  this.window = null;
  /* Sliding window. Input bytes are read into the second half of the window,
   * and move to the first half later to keep a dictionary of at least wSize
   * bytes. With this organization, matches are limited to a distance of
   * wSize-MAX_MATCH bytes, but this ensures that IO is always
   * performed with a length multiple of the block size.
   */

  this.window_size = 0;
  /* Actual size of window: 2*wSize, except when the user input buffer
   * is directly used as sliding window.
   */

  this.prev = null;
  /* Link to older string with same hash index. To limit the size of this
   * array to 64K, this link is maintained only for the last 32K strings.
   * An index in this array is thus a window index modulo 32K.
   */

  this.head = null;   /* Heads of the hash chains or NIL. */

  this.ins_h = 0;       /* hash index of string to be inserted */
  this.hash_size = 0;   /* number of elements in hash table */
  this.hash_bits = 0;   /* log2(hash_size) */
  this.hash_mask = 0;   /* hash_size-1 */

  this.hash_shift = 0;
  /* Number of bits by which ins_h must be shifted at each input
   * step. It must be such that after MIN_MATCH steps, the oldest
   * byte no longer takes part in the hash key, that is:
   *   hash_shift * MIN_MATCH >= hash_bits
   */

  this.block_start = 0;
  /* Window position at the beginning of the current output block. Gets
   * negative when the window is moved backwards.
   */

  this.match_length = 0;      /* length of best match */
  this.prev_match = 0;        /* previous match */
  this.match_available = 0;   /* set if previous match exists */
  this.strstart = 0;          /* start of string to insert */
  this.match_start = 0;       /* start of matching string */
  this.lookahead = 0;         /* number of valid bytes ahead in window */

  this.prev_length = 0;
  /* Length of the best match at previous step. Matches not greater than this
   * are discarded. This is used in the lazy match evaluation.
   */

  this.max_chain_length = 0;
  /* To speed up deflation, hash chains are never searched beyond this
   * length.  A higher limit improves compression ratio but degrades the
   * speed.
   */

  this.max_lazy_match = 0;
  /* Attempt to find a better match only when the current match is strictly
   * smaller than this value. This mechanism is used only for compression
   * levels >= 4.
   */
  // That's alias to max_lazy_match, don't use directly
  //this.max_insert_length = 0;
  /* Insert new strings in the hash table only if the match length is not
   * greater than this length. This saves time but degrades compression.
   * max_insert_length is used only for compression levels <= 3.
   */

  this.level = 0;     /* compression level (1..9) */
  this.strategy = 0;  /* favor or force Huffman coding*/

  this.good_match = 0;
  /* Use a faster search when the previous match is longer than this */

  this.nice_match = 0; /* Stop searching when current match exceeds this */

              /* used by trees.c: */

  /* Didn't use ct_data typedef below to suppress compiler warning */

  // struct ct_data_s dyn_ltree[HEAP_SIZE];   /* literal and length tree */
  // struct ct_data_s dyn_dtree[2*D_CODES+1]; /* distance tree */
  // struct ct_data_s bl_tree[2*BL_CODES+1];  /* Huffman tree for bit lengths */

  // Use flat array of DOUBLE size, with interleaved fata,
  // because JS does not support effective
  this.dyn_ltree  = new utils.Buf16(HEAP_SIZE * 2);
  this.dyn_dtree  = new utils.Buf16((2 * D_CODES + 1) * 2);
  this.bl_tree    = new utils.Buf16((2 * BL_CODES + 1) * 2);
  zero(this.dyn_ltree);
  zero(this.dyn_dtree);
  zero(this.bl_tree);

  this.l_desc   = null;         /* desc. for literal tree */
  this.d_desc   = null;         /* desc. for distance tree */
  this.bl_desc  = null;         /* desc. for bit length tree */

  //ush bl_count[MAX_BITS+1];
  this.bl_count = new utils.Buf16(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  //int heap[2*L_CODES+1];      /* heap used to build the Huffman trees */
  this.heap = new utils.Buf16(2 * L_CODES + 1);  /* heap used to build the Huffman trees */
  zero(this.heap);

  this.heap_len = 0;               /* number of elements in the heap */
  this.heap_max = 0;               /* element of largest frequency */
  /* The sons of heap[n] are heap[2*n] and heap[2*n+1]. heap[0] is not used.
   * The same heap array is used to build all trees.
   */

  this.depth = new utils.Buf16(2 * L_CODES + 1); //uch depth[2*L_CODES+1];
  zero(this.depth);
  /* Depth of each subtree used as tie breaker for trees of equal frequency
   */

  this.l_buf = 0;          /* buffer index for literals or lengths */

  this.lit_bufsize = 0;
  /* Size of match buffer for literals/lengths.  There are 4 reasons for
   * limiting lit_bufsize to 64K:
   *   - frequencies can be kept in 16 bit counters
   *   - if compression is not successful for the first block, all input
   *     data is still in the window so we can still emit a stored block even
   *     when input comes from standard input.  (This can also be done for
   *     all blocks if lit_bufsize is not greater than 32K.)
   *   - if compression is not successful for a file smaller than 64K, we can
   *     even emit a stored file instead of a stored block (saving 5 bytes).
   *     This is applicable only for zip (not gzip or zlib).
   *   - creating new Huffman trees less frequently may not provide fast
   *     adaptation to changes in the input data statistics. (Take for
   *     example a binary file with poorly compressible code followed by
   *     a highly compressible string table.) Smaller buffer sizes give
   *     fast adaptation but have of course the overhead of transmitting
   *     trees more frequently.
   *   - I can't count above 4
   */

  this.last_lit = 0;      /* running index in l_buf */

  this.d_buf = 0;
  /* Buffer index for distances. To simplify the code, d_buf and l_buf have
   * the same number of elements. To use different lengths, an extra flag
   * array would be necessary.
   */

  this.opt_len = 0;       /* bit length of current block with optimal trees */
  this.static_len = 0;    /* bit length of current block with static trees */
  this.matches = 0;       /* number of string matches in current block */
  this.insert = 0;        /* bytes at end of window left to insert */


  this.bi_buf = 0;
  /* Output buffer. bits are inserted starting at the bottom (least
   * significant bits).
   */
  this.bi_valid = 0;
  /* Number of valid bits in bi_buf.  All bits above the last valid bit
   * are always zero.
   */

  // Used for window memory init. We safely ignore it for JS. That makes
  // sense only for pointers and memory check tools.
  //this.high_water = 0;
  /* High water mark offset in window for initialized bytes -- bytes above
   * this are set to zero in order to avoid memory check warnings when
   * longest match routines access bytes past the input.  This is then
   * updated to the new high water mark.
   */
}


function deflateResetKeep(strm) {
  var s;

  if (!strm || !strm.state) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.total_in = strm.total_out = 0;
  strm.data_type = Z_UNKNOWN;

  s = strm.state;
  s.pending = 0;
  s.pending_out = 0;

  if (s.wrap < 0) {
    s.wrap = -s.wrap;
    /* was made negative by deflate(..., Z_FINISH); */
  }
  s.status = (s.wrap ? INIT_STATE : BUSY_STATE);
  strm.adler = (s.wrap === 2) ?
    0  // crc32(0, Z_NULL, 0)
  :
    1; // adler32(0, Z_NULL, 0)
  s.last_flush = Z_NO_FLUSH;
  trees._tr_init(s);
  return Z_OK;
}


function deflateReset(strm) {
  var ret = deflateResetKeep(strm);
  if (ret === Z_OK) {
    lm_init(strm.state);
  }
  return ret;
}


function deflateSetHeader(strm, head) {
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  if (strm.state.wrap !== 2) { return Z_STREAM_ERROR; }
  strm.state.gzhead = head;
  return Z_OK;
}


function deflateInit2(strm, level, method, windowBits, memLevel, strategy) {
  if (!strm) { // === Z_NULL
    return Z_STREAM_ERROR;
  }
  var wrap = 1;

  if (level === Z_DEFAULT_COMPRESSION) {
    level = 6;
  }

  if (windowBits < 0) { /* suppress zlib wrapper */
    wrap = 0;
    windowBits = -windowBits;
  }

  else if (windowBits > 15) {
    wrap = 2;           /* write gzip wrapper instead */
    windowBits -= 16;
  }


  if (memLevel < 1 || memLevel > MAX_MEM_LEVEL || method !== Z_DEFLATED ||
    windowBits < 8 || windowBits > 15 || level < 0 || level > 9 ||
    strategy < 0 || strategy > Z_FIXED) {
    return err(strm, Z_STREAM_ERROR);
  }


  if (windowBits === 8) {
    windowBits = 9;
  }
  /* until 256-byte window bug fixed */

  var s = new DeflateState();

  strm.state = s;
  s.strm = strm;

  s.wrap = wrap;
  s.gzhead = null;
  s.w_bits = windowBits;
  s.w_size = 1 << s.w_bits;
  s.w_mask = s.w_size - 1;

  s.hash_bits = memLevel + 7;
  s.hash_size = 1 << s.hash_bits;
  s.hash_mask = s.hash_size - 1;
  s.hash_shift = ~~((s.hash_bits + MIN_MATCH - 1) / MIN_MATCH);

  s.window = new utils.Buf8(s.w_size * 2);
  s.head = new utils.Buf16(s.hash_size);
  s.prev = new utils.Buf16(s.w_size);

  // Don't need mem init magic for JS.
  //s.high_water = 0;  /* nothing written to s->window yet */

  s.lit_bufsize = 1 << (memLevel + 6); /* 16K elements by default */

  s.pending_buf_size = s.lit_bufsize * 4;

  //overlay = (ushf *) ZALLOC(strm, s->lit_bufsize, sizeof(ush)+2);
  //s->pending_buf = (uchf *) overlay;
  s.pending_buf = new utils.Buf8(s.pending_buf_size);

  // It is offset from `s.pending_buf` (size is `s.lit_bufsize * 2`)
  //s->d_buf = overlay + s->lit_bufsize/sizeof(ush);
  s.d_buf = 1 * s.lit_bufsize;

  //s->l_buf = s->pending_buf + (1+sizeof(ush))*s->lit_bufsize;
  s.l_buf = (1 + 2) * s.lit_bufsize;

  s.level = level;
  s.strategy = strategy;
  s.method = method;

  return deflateReset(strm);
}

function deflateInit(strm, level) {
  return deflateInit2(strm, level, Z_DEFLATED, MAX_WBITS, DEF_MEM_LEVEL, Z_DEFAULT_STRATEGY);
}


function deflate(strm, flush) {
  var old_flush, s;
  var beg, val; // for gzip header write only

  if (!strm || !strm.state ||
    flush > Z_BLOCK || flush < 0) {
    return strm ? err(strm, Z_STREAM_ERROR) : Z_STREAM_ERROR;
  }

  s = strm.state;

  if (!strm.output ||
      (!strm.input && strm.avail_in !== 0) ||
      (s.status === FINISH_STATE && flush !== Z_FINISH)) {
    return err(strm, (strm.avail_out === 0) ? Z_BUF_ERROR : Z_STREAM_ERROR);
  }

  s.strm = strm; /* just in case */
  old_flush = s.last_flush;
  s.last_flush = flush;

  /* Write the header */
  if (s.status === INIT_STATE) {

    if (s.wrap === 2) { // GZIP header
      strm.adler = 0;  //crc32(0L, Z_NULL, 0);
      put_byte(s, 31);
      put_byte(s, 139);
      put_byte(s, 8);
      if (!s.gzhead) { // s->gzhead == Z_NULL
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, 0);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, OS_CODE);
        s.status = BUSY_STATE;
      }
      else {
        put_byte(s, (s.gzhead.text ? 1 : 0) +
                    (s.gzhead.hcrc ? 2 : 0) +
                    (!s.gzhead.extra ? 0 : 4) +
                    (!s.gzhead.name ? 0 : 8) +
                    (!s.gzhead.comment ? 0 : 16)
                );
        put_byte(s, s.gzhead.time & 0xff);
        put_byte(s, (s.gzhead.time >> 8) & 0xff);
        put_byte(s, (s.gzhead.time >> 16) & 0xff);
        put_byte(s, (s.gzhead.time >> 24) & 0xff);
        put_byte(s, s.level === 9 ? 2 :
                    (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2 ?
                     4 : 0));
        put_byte(s, s.gzhead.os & 0xff);
        if (s.gzhead.extra && s.gzhead.extra.length) {
          put_byte(s, s.gzhead.extra.length & 0xff);
          put_byte(s, (s.gzhead.extra.length >> 8) & 0xff);
        }
        if (s.gzhead.hcrc) {
          strm.adler = crc32(strm.adler, s.pending_buf, s.pending, 0);
        }
        s.gzindex = 0;
        s.status = EXTRA_STATE;
      }
    }
    else // DEFLATE header
    {
      var header = (Z_DEFLATED + ((s.w_bits - 8) << 4)) << 8;
      var level_flags = -1;

      if (s.strategy >= Z_HUFFMAN_ONLY || s.level < 2) {
        level_flags = 0;
      } else if (s.level < 6) {
        level_flags = 1;
      } else if (s.level === 6) {
        level_flags = 2;
      } else {
        level_flags = 3;
      }
      header |= (level_flags << 6);
      if (s.strstart !== 0) { header |= PRESET_DICT; }
      header += 31 - (header % 31);

      s.status = BUSY_STATE;
      putShortMSB(s, header);

      /* Save the adler32 of the preset dictionary: */
      if (s.strstart !== 0) {
        putShortMSB(s, strm.adler >>> 16);
        putShortMSB(s, strm.adler & 0xffff);
      }
      strm.adler = 1; // adler32(0L, Z_NULL, 0);
    }
  }

//#ifdef GZIP
  if (s.status === EXTRA_STATE) {
    if (s.gzhead.extra/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */

      while (s.gzindex < (s.gzhead.extra.length & 0xffff)) {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            break;
          }
        }
        put_byte(s, s.gzhead.extra[s.gzindex] & 0xff);
        s.gzindex++;
      }
      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (s.gzindex === s.gzhead.extra.length) {
        s.gzindex = 0;
        s.status = NAME_STATE;
      }
    }
    else {
      s.status = NAME_STATE;
    }
  }
  if (s.status === NAME_STATE) {
    if (s.gzhead.name/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.name.length) {
          val = s.gzhead.name.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.gzindex = 0;
        s.status = COMMENT_STATE;
      }
    }
    else {
      s.status = COMMENT_STATE;
    }
  }
  if (s.status === COMMENT_STATE) {
    if (s.gzhead.comment/* != Z_NULL*/) {
      beg = s.pending;  /* start of bytes to update crc */
      //int val;

      do {
        if (s.pending === s.pending_buf_size) {
          if (s.gzhead.hcrc && s.pending > beg) {
            strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
          }
          flush_pending(strm);
          beg = s.pending;
          if (s.pending === s.pending_buf_size) {
            val = 1;
            break;
          }
        }
        // JS specific: little magic to add zero terminator to end of string
        if (s.gzindex < s.gzhead.comment.length) {
          val = s.gzhead.comment.charCodeAt(s.gzindex++) & 0xff;
        } else {
          val = 0;
        }
        put_byte(s, val);
      } while (val !== 0);

      if (s.gzhead.hcrc && s.pending > beg) {
        strm.adler = crc32(strm.adler, s.pending_buf, s.pending - beg, beg);
      }
      if (val === 0) {
        s.status = HCRC_STATE;
      }
    }
    else {
      s.status = HCRC_STATE;
    }
  }
  if (s.status === HCRC_STATE) {
    if (s.gzhead.hcrc) {
      if (s.pending + 2 > s.pending_buf_size) {
        flush_pending(strm);
      }
      if (s.pending + 2 <= s.pending_buf_size) {
        put_byte(s, strm.adler & 0xff);
        put_byte(s, (strm.adler >> 8) & 0xff);
        strm.adler = 0; //crc32(0L, Z_NULL, 0);
        s.status = BUSY_STATE;
      }
    }
    else {
      s.status = BUSY_STATE;
    }
  }
//#endif

  /* Flush as much pending output as possible */
  if (s.pending !== 0) {
    flush_pending(strm);
    if (strm.avail_out === 0) {
      /* Since avail_out is 0, deflate will be called again with
       * more output space, but possibly with both pending and
       * avail_in equal to zero. There won't be anything to do,
       * but this is not an error situation so make sure we
       * return OK instead of BUF_ERROR at next call of deflate:
       */
      s.last_flush = -1;
      return Z_OK;
    }

    /* Make sure there is something to do and avoid duplicate consecutive
     * flushes. For repeated and useless calls with Z_FINISH, we keep
     * returning Z_STREAM_END instead of Z_BUF_ERROR.
     */
  } else if (strm.avail_in === 0 && rank(flush) <= rank(old_flush) &&
    flush !== Z_FINISH) {
    return err(strm, Z_BUF_ERROR);
  }

  /* User must not provide more input after the first FINISH: */
  if (s.status === FINISH_STATE && strm.avail_in !== 0) {
    return err(strm, Z_BUF_ERROR);
  }

  /* Start a new block or continue the current one.
   */
  if (strm.avail_in !== 0 || s.lookahead !== 0 ||
    (flush !== Z_NO_FLUSH && s.status !== FINISH_STATE)) {
    var bstate = (s.strategy === Z_HUFFMAN_ONLY) ? deflate_huff(s, flush) :
      (s.strategy === Z_RLE ? deflate_rle(s, flush) :
        configuration_table[s.level].func(s, flush));

    if (bstate === BS_FINISH_STARTED || bstate === BS_FINISH_DONE) {
      s.status = FINISH_STATE;
    }
    if (bstate === BS_NEED_MORE || bstate === BS_FINISH_STARTED) {
      if (strm.avail_out === 0) {
        s.last_flush = -1;
        /* avoid BUF_ERROR next call, see above */
      }
      return Z_OK;
      /* If flush != Z_NO_FLUSH && avail_out == 0, the next call
       * of deflate should use the same flush parameter to make sure
       * that the flush is complete. So we don't have to output an
       * empty block here, this will be done at next call. This also
       * ensures that for a very small output buffer, we emit at most
       * one empty block.
       */
    }
    if (bstate === BS_BLOCK_DONE) {
      if (flush === Z_PARTIAL_FLUSH) {
        trees._tr_align(s);
      }
      else if (flush !== Z_BLOCK) { /* FULL_FLUSH or SYNC_FLUSH */

        trees._tr_stored_block(s, 0, 0, false);
        /* For a full flush, this empty block will be recognized
         * as a special marker by inflate_sync().
         */
        if (flush === Z_FULL_FLUSH) {
          /*** CLEAR_HASH(s); ***/             /* forget history */
          zero(s.head); // Fill with NIL (= 0);

          if (s.lookahead === 0) {
            s.strstart = 0;
            s.block_start = 0;
            s.insert = 0;
          }
        }
      }
      flush_pending(strm);
      if (strm.avail_out === 0) {
        s.last_flush = -1; /* avoid BUF_ERROR at next call, see above */
        return Z_OK;
      }
    }
  }
  //Assert(strm->avail_out > 0, "bug2");
  //if (strm.avail_out <= 0) { throw new Error("bug2");}

  if (flush !== Z_FINISH) { return Z_OK; }
  if (s.wrap <= 0) { return Z_STREAM_END; }

  /* Write the trailer */
  if (s.wrap === 2) {
    put_byte(s, strm.adler & 0xff);
    put_byte(s, (strm.adler >> 8) & 0xff);
    put_byte(s, (strm.adler >> 16) & 0xff);
    put_byte(s, (strm.adler >> 24) & 0xff);
    put_byte(s, strm.total_in & 0xff);
    put_byte(s, (strm.total_in >> 8) & 0xff);
    put_byte(s, (strm.total_in >> 16) & 0xff);
    put_byte(s, (strm.total_in >> 24) & 0xff);
  }
  else
  {
    putShortMSB(s, strm.adler >>> 16);
    putShortMSB(s, strm.adler & 0xffff);
  }

  flush_pending(strm);
  /* If avail_out is zero, the application will call deflate again
   * to flush the rest.
   */
  if (s.wrap > 0) { s.wrap = -s.wrap; }
  /* write the trailer only once! */
  return s.pending !== 0 ? Z_OK : Z_STREAM_END;
}

function deflateEnd(strm) {
  var status;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  status = strm.state.status;
  if (status !== INIT_STATE &&
    status !== EXTRA_STATE &&
    status !== NAME_STATE &&
    status !== COMMENT_STATE &&
    status !== HCRC_STATE &&
    status !== BUSY_STATE &&
    status !== FINISH_STATE
  ) {
    return err(strm, Z_STREAM_ERROR);
  }

  strm.state = null;

  return status === BUSY_STATE ? err(strm, Z_DATA_ERROR) : Z_OK;
}


/* =========================================================================
 * Initializes the compression dictionary from the given byte
 * sequence without producing any compressed output.
 */
function deflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var s;
  var str, n;
  var wrap;
  var avail;
  var next;
  var input;
  var tmpDict;

  if (!strm/*== Z_NULL*/ || !strm.state/*== Z_NULL*/) {
    return Z_STREAM_ERROR;
  }

  s = strm.state;
  wrap = s.wrap;

  if (wrap === 2 || (wrap === 1 && s.status !== INIT_STATE) || s.lookahead) {
    return Z_STREAM_ERROR;
  }

  /* when using zlib wrappers, compute Adler-32 for provided dictionary */
  if (wrap === 1) {
    /* adler32(strm->adler, dictionary, dictLength); */
    strm.adler = adler32(strm.adler, dictionary, dictLength, 0);
  }

  s.wrap = 0;   /* avoid computing Adler-32 in read_buf */

  /* if dictionary would fill window, just replace the history */
  if (dictLength >= s.w_size) {
    if (wrap === 0) {            /* already empty otherwise */
      /*** CLEAR_HASH(s); ***/
      zero(s.head); // Fill with NIL (= 0);
      s.strstart = 0;
      s.block_start = 0;
      s.insert = 0;
    }
    /* use the tail */
    // dictionary = dictionary.slice(dictLength - s.w_size);
    tmpDict = new utils.Buf8(s.w_size);
    utils.arraySet(tmpDict, dictionary, dictLength - s.w_size, s.w_size, 0);
    dictionary = tmpDict;
    dictLength = s.w_size;
  }
  /* insert dictionary into window and hash */
  avail = strm.avail_in;
  next = strm.next_in;
  input = strm.input;
  strm.avail_in = dictLength;
  strm.next_in = 0;
  strm.input = dictionary;
  fill_window(s);
  while (s.lookahead >= MIN_MATCH) {
    str = s.strstart;
    n = s.lookahead - (MIN_MATCH - 1);
    do {
      /* UPDATE_HASH(s, s->ins_h, s->window[str + MIN_MATCH-1]); */
      s.ins_h = ((s.ins_h << s.hash_shift) ^ s.window[str + MIN_MATCH - 1]) & s.hash_mask;

      s.prev[str & s.w_mask] = s.head[s.ins_h];

      s.head[s.ins_h] = str;
      str++;
    } while (--n);
    s.strstart = str;
    s.lookahead = MIN_MATCH - 1;
    fill_window(s);
  }
  s.strstart += s.lookahead;
  s.block_start = s.strstart;
  s.insert = s.lookahead;
  s.lookahead = 0;
  s.match_length = s.prev_length = MIN_MATCH - 1;
  s.match_available = 0;
  strm.next_in = next;
  strm.input = input;
  strm.avail_in = avail;
  s.wrap = wrap;
  return Z_OK;
}


exports.deflateInit = deflateInit;
exports.deflateInit2 = deflateInit2;
exports.deflateReset = deflateReset;
exports.deflateResetKeep = deflateResetKeep;
exports.deflateSetHeader = deflateSetHeader;
exports.deflate = deflate;
exports.deflateEnd = deflateEnd;
exports.deflateSetDictionary = deflateSetDictionary;
exports.deflateInfo = 'pako deflate (from Nodeca project)';

/* Not implemented
exports.deflateBound = deflateBound;
exports.deflateCopy = deflateCopy;
exports.deflateParams = deflateParams;
exports.deflatePending = deflatePending;
exports.deflatePrime = deflatePrime;
exports.deflateTune = deflateTune;
*/

},{"../utils/common":41,"./adler32":43,"./crc32":45,"./messages":51,"./trees":52}],47:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function GZheader() {
  /* true if compressed data believed to be text */
  this.text       = 0;
  /* modification time */
  this.time       = 0;
  /* extra flags (not used when writing a gzip file) */
  this.xflags     = 0;
  /* operating system */
  this.os         = 0;
  /* pointer to extra field or Z_NULL if none */
  this.extra      = null;
  /* extra field length (valid if extra != Z_NULL) */
  this.extra_len  = 0; // Actually, we don't need it in JS,
                       // but leave for few code modifications

  //
  // Setup limits is not necessary because in js we should not preallocate memory
  // for inflate use constant limit in 65536 bytes
  //

  /* space at extra (only when reading header) */
  // this.extra_max  = 0;
  /* pointer to zero-terminated file name or Z_NULL */
  this.name       = '';
  /* space at name (only when reading header) */
  // this.name_max   = 0;
  /* pointer to zero-terminated comment or Z_NULL */
  this.comment    = '';
  /* space at comment (only when reading header) */
  // this.comm_max   = 0;
  /* true if there was or will be a header crc */
  this.hcrc       = 0;
  /* true when done reading gzip header (not used when writing a gzip file) */
  this.done       = false;
}

module.exports = GZheader;

},{}],48:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

// See state defs from inflate.js
var BAD = 30;       /* got a data error -- remain here until reset */
var TYPE = 12;      /* i: waiting for type bits, including last-flag bit */

/*
   Decode literal, length, and distance codes and write out the resulting
   literal and match bytes until either not enough input or output is
   available, an end-of-block is encountered, or a data error is encountered.
   When large enough input and output buffers are supplied to inflate(), for
   example, a 16K input buffer and a 64K output buffer, more than 95% of the
   inflate execution time is spent in this routine.

   Entry assumptions:

        state.mode === LEN
        strm.avail_in >= 6
        strm.avail_out >= 258
        start >= strm.avail_out
        state.bits < 8

   On return, state.mode is one of:

        LEN -- ran out of enough output space or enough available input
        TYPE -- reached end of block code, inflate() to interpret next block
        BAD -- error in block data

   Notes:

    - The maximum input bits used by a length/distance pair is 15 bits for the
      length code, 5 bits for the length extra, 15 bits for the distance code,
      and 13 bits for the distance extra.  This totals 48 bits, or six bytes.
      Therefore if strm.avail_in >= 6, then there is enough input to avoid
      checking for available input while decoding.

    - The maximum bytes that a single length/distance pair can output is 258
      bytes, which is the maximum length that can be coded.  inflate_fast()
      requires strm.avail_out >= 258 for each loop to avoid checking for
      output space.
 */
module.exports = function inflate_fast(strm, start) {
  var state;
  var _in;                    /* local strm.input */
  var last;                   /* have enough input while in < last */
  var _out;                   /* local strm.output */
  var beg;                    /* inflate()'s initial strm.output */
  var end;                    /* while out < end, enough space available */
//#ifdef INFLATE_STRICT
  var dmax;                   /* maximum distance from zlib header */
//#endif
  var wsize;                  /* window size or zero if not using window */
  var whave;                  /* valid bytes in the window */
  var wnext;                  /* window write index */
  // Use `s_window` instead `window`, avoid conflict with instrumentation tools
  var s_window;               /* allocated sliding window, if wsize != 0 */
  var hold;                   /* local strm.hold */
  var bits;                   /* local strm.bits */
  var lcode;                  /* local strm.lencode */
  var dcode;                  /* local strm.distcode */
  var lmask;                  /* mask for first level of length codes */
  var dmask;                  /* mask for first level of distance codes */
  var here;                   /* retrieved table entry */
  var op;                     /* code bits, operation, extra bits, or */
                              /*  window position, window bytes to copy */
  var len;                    /* match length, unused bytes */
  var dist;                   /* match distance */
  var from;                   /* where to copy match from */
  var from_source;


  var input, output; // JS specific, because we have no pointers

  /* copy state to local variables */
  state = strm.state;
  //here = state.here;
  _in = strm.next_in;
  input = strm.input;
  last = _in + (strm.avail_in - 5);
  _out = strm.next_out;
  output = strm.output;
  beg = _out - (start - strm.avail_out);
  end = _out + (strm.avail_out - 257);
//#ifdef INFLATE_STRICT
  dmax = state.dmax;
//#endif
  wsize = state.wsize;
  whave = state.whave;
  wnext = state.wnext;
  s_window = state.window;
  hold = state.hold;
  bits = state.bits;
  lcode = state.lencode;
  dcode = state.distcode;
  lmask = (1 << state.lenbits) - 1;
  dmask = (1 << state.distbits) - 1;


  /* decode literals and length/distances until end-of-block or not enough
     input data or output space */

  top:
  do {
    if (bits < 15) {
      hold += input[_in++] << bits;
      bits += 8;
      hold += input[_in++] << bits;
      bits += 8;
    }

    here = lcode[hold & lmask];

    dolen:
    for (;;) { // Goto emulation
      op = here >>> 24/*here.bits*/;
      hold >>>= op;
      bits -= op;
      op = (here >>> 16) & 0xff/*here.op*/;
      if (op === 0) {                          /* literal */
        //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
        //        "inflate:         literal '%c'\n" :
        //        "inflate:         literal 0x%02x\n", here.val));
        output[_out++] = here & 0xffff/*here.val*/;
      }
      else if (op & 16) {                     /* length base */
        len = here & 0xffff/*here.val*/;
        op &= 15;                           /* number of extra bits */
        if (op) {
          if (bits < op) {
            hold += input[_in++] << bits;
            bits += 8;
          }
          len += hold & ((1 << op) - 1);
          hold >>>= op;
          bits -= op;
        }
        //Tracevv((stderr, "inflate:         length %u\n", len));
        if (bits < 15) {
          hold += input[_in++] << bits;
          bits += 8;
          hold += input[_in++] << bits;
          bits += 8;
        }
        here = dcode[hold & dmask];

        dodist:
        for (;;) { // goto emulation
          op = here >>> 24/*here.bits*/;
          hold >>>= op;
          bits -= op;
          op = (here >>> 16) & 0xff/*here.op*/;

          if (op & 16) {                      /* distance base */
            dist = here & 0xffff/*here.val*/;
            op &= 15;                       /* number of extra bits */
            if (bits < op) {
              hold += input[_in++] << bits;
              bits += 8;
              if (bits < op) {
                hold += input[_in++] << bits;
                bits += 8;
              }
            }
            dist += hold & ((1 << op) - 1);
//#ifdef INFLATE_STRICT
            if (dist > dmax) {
              strm.msg = 'invalid distance too far back';
              state.mode = BAD;
              break top;
            }
//#endif
            hold >>>= op;
            bits -= op;
            //Tracevv((stderr, "inflate:         distance %u\n", dist));
            op = _out - beg;                /* max distance in output */
            if (dist > op) {                /* see if copy from window */
              op = dist - op;               /* distance back in window */
              if (op > whave) {
                if (state.sane) {
                  strm.msg = 'invalid distance too far back';
                  state.mode = BAD;
                  break top;
                }

// (!) This block is disabled in zlib defailts,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//                if (len <= op - whave) {
//                  do {
//                    output[_out++] = 0;
//                  } while (--len);
//                  continue top;
//                }
//                len -= op - whave;
//                do {
//                  output[_out++] = 0;
//                } while (--op > whave);
//                if (op === 0) {
//                  from = _out - dist;
//                  do {
//                    output[_out++] = output[from++];
//                  } while (--len);
//                  continue top;
//                }
//#endif
              }
              from = 0; // window index
              from_source = s_window;
              if (wnext === 0) {           /* very common case */
                from += wsize - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              else if (wnext < op) {      /* wrap around window */
                from += wsize + wnext - op;
                op -= wnext;
                if (op < len) {         /* some from end of window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = 0;
                  if (wnext < len) {  /* some from start of window */
                    op = wnext;
                    len -= op;
                    do {
                      output[_out++] = s_window[from++];
                    } while (--op);
                    from = _out - dist;      /* rest from output */
                    from_source = output;
                  }
                }
              }
              else {                      /* contiguous in window */
                from += wnext - op;
                if (op < len) {         /* some from window */
                  len -= op;
                  do {
                    output[_out++] = s_window[from++];
                  } while (--op);
                  from = _out - dist;  /* rest from output */
                  from_source = output;
                }
              }
              while (len > 2) {
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                output[_out++] = from_source[from++];
                len -= 3;
              }
              if (len) {
                output[_out++] = from_source[from++];
                if (len > 1) {
                  output[_out++] = from_source[from++];
                }
              }
            }
            else {
              from = _out - dist;          /* copy direct from output */
              do {                        /* minimum length is three */
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                output[_out++] = output[from++];
                len -= 3;
              } while (len > 2);
              if (len) {
                output[_out++] = output[from++];
                if (len > 1) {
                  output[_out++] = output[from++];
                }
              }
            }
          }
          else if ((op & 64) === 0) {          /* 2nd level distance code */
            here = dcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
            continue dodist;
          }
          else {
            strm.msg = 'invalid distance code';
            state.mode = BAD;
            break top;
          }

          break; // need to emulate goto via "continue"
        }
      }
      else if ((op & 64) === 0) {              /* 2nd level length code */
        here = lcode[(here & 0xffff)/*here.val*/ + (hold & ((1 << op) - 1))];
        continue dolen;
      }
      else if (op & 32) {                     /* end-of-block */
        //Tracevv((stderr, "inflate:         end of block\n"));
        state.mode = TYPE;
        break top;
      }
      else {
        strm.msg = 'invalid literal/length code';
        state.mode = BAD;
        break top;
      }

      break; // need to emulate goto via "continue"
    }
  } while (_in < last && _out < end);

  /* return unused bytes (on entry, bits < 8, so in won't go too far back) */
  len = bits >> 3;
  _in -= len;
  bits -= len << 3;
  hold &= (1 << bits) - 1;

  /* update state and return */
  strm.next_in = _in;
  strm.next_out = _out;
  strm.avail_in = (_in < last ? 5 + (last - _in) : 5 - (_in - last));
  strm.avail_out = (_out < end ? 257 + (end - _out) : 257 - (_out - end));
  state.hold = hold;
  state.bits = bits;
  return;
};

},{}],49:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils         = require('../utils/common');
var adler32       = require('./adler32');
var crc32         = require('./crc32');
var inflate_fast  = require('./inffast');
var inflate_table = require('./inftrees');

var CODES = 0;
var LENS = 1;
var DISTS = 2;

/* Public constants ==========================================================*/
/* ===========================================================================*/


/* Allowed flush values; see deflate() and inflate() below for details */
//var Z_NO_FLUSH      = 0;
//var Z_PARTIAL_FLUSH = 1;
//var Z_SYNC_FLUSH    = 2;
//var Z_FULL_FLUSH    = 3;
var Z_FINISH        = 4;
var Z_BLOCK         = 5;
var Z_TREES         = 6;


/* Return codes for the compression/decompression functions. Negative values
 * are errors, positive values are used for special but normal events.
 */
var Z_OK            = 0;
var Z_STREAM_END    = 1;
var Z_NEED_DICT     = 2;
//var Z_ERRNO         = -1;
var Z_STREAM_ERROR  = -2;
var Z_DATA_ERROR    = -3;
var Z_MEM_ERROR     = -4;
var Z_BUF_ERROR     = -5;
//var Z_VERSION_ERROR = -6;

/* The deflate compression method */
var Z_DEFLATED  = 8;


/* STATES ====================================================================*/
/* ===========================================================================*/


var    HEAD = 1;       /* i: waiting for magic header */
var    FLAGS = 2;      /* i: waiting for method and flags (gzip) */
var    TIME = 3;       /* i: waiting for modification time (gzip) */
var    OS = 4;         /* i: waiting for extra flags and operating system (gzip) */
var    EXLEN = 5;      /* i: waiting for extra length (gzip) */
var    EXTRA = 6;      /* i: waiting for extra bytes (gzip) */
var    NAME = 7;       /* i: waiting for end of file name (gzip) */
var    COMMENT = 8;    /* i: waiting for end of comment (gzip) */
var    HCRC = 9;       /* i: waiting for header crc (gzip) */
var    DICTID = 10;    /* i: waiting for dictionary check value */
var    DICT = 11;      /* waiting for inflateSetDictionary() call */
var        TYPE = 12;      /* i: waiting for type bits, including last-flag bit */
var        TYPEDO = 13;    /* i: same, but skip check to exit inflate on new block */
var        STORED = 14;    /* i: waiting for stored size (length and complement) */
var        COPY_ = 15;     /* i/o: same as COPY below, but only first time in */
var        COPY = 16;      /* i/o: waiting for input or output to copy stored block */
var        TABLE = 17;     /* i: waiting for dynamic block table lengths */
var        LENLENS = 18;   /* i: waiting for code length code lengths */
var        CODELENS = 19;  /* i: waiting for length/lit and distance code lengths */
var            LEN_ = 20;      /* i: same as LEN below, but only first time in */
var            LEN = 21;       /* i: waiting for length/lit/eob code */
var            LENEXT = 22;    /* i: waiting for length extra bits */
var            DIST = 23;      /* i: waiting for distance code */
var            DISTEXT = 24;   /* i: waiting for distance extra bits */
var            MATCH = 25;     /* o: waiting for output space to copy string */
var            LIT = 26;       /* o: waiting for output space to write literal */
var    CHECK = 27;     /* i: waiting for 32-bit check value */
var    LENGTH = 28;    /* i: waiting for 32-bit length (gzip) */
var    DONE = 29;      /* finished check, done -- remain here until reset */
var    BAD = 30;       /* got a data error -- remain here until reset */
var    MEM = 31;       /* got an inflate() memory error -- remain here until reset */
var    SYNC = 32;      /* looking for synchronization bytes to restart inflate() */

/* ===========================================================================*/



var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH =  (ENOUGH_LENS+ENOUGH_DISTS);

var MAX_WBITS = 15;
/* 32K LZ77 window */
var DEF_WBITS = MAX_WBITS;


function zswap32(q) {
  return  (((q >>> 24) & 0xff) +
          ((q >>> 8) & 0xff00) +
          ((q & 0xff00) << 8) +
          ((q & 0xff) << 24));
}


function InflateState() {
  this.mode = 0;             /* current inflate mode */
  this.last = false;          /* true if processing last block */
  this.wrap = 0;              /* bit 0 true for zlib, bit 1 true for gzip */
  this.havedict = false;      /* true if dictionary provided */
  this.flags = 0;             /* gzip header method and flags (0 if zlib) */
  this.dmax = 0;              /* zlib header max distance (INFLATE_STRICT) */
  this.check = 0;             /* protected copy of check value */
  this.total = 0;             /* protected copy of output count */
  // TODO: may be {}
  this.head = null;           /* where to save gzip header information */

  /* sliding window */
  this.wbits = 0;             /* log base 2 of requested window size */
  this.wsize = 0;             /* window size or zero if not using window */
  this.whave = 0;             /* valid bytes in the window */
  this.wnext = 0;             /* window write index */
  this.window = null;         /* allocated sliding window, if needed */

  /* bit accumulator */
  this.hold = 0;              /* input bit accumulator */
  this.bits = 0;              /* number of bits in "in" */

  /* for string and stored block copying */
  this.length = 0;            /* literal or length of data to copy */
  this.offset = 0;            /* distance back to copy string from */

  /* for table and code decoding */
  this.extra = 0;             /* extra bits needed */

  /* fixed and dynamic code tables */
  this.lencode = null;          /* starting table for length/literal codes */
  this.distcode = null;         /* starting table for distance codes */
  this.lenbits = 0;           /* index bits for lencode */
  this.distbits = 0;          /* index bits for distcode */

  /* dynamic table building */
  this.ncode = 0;             /* number of code length code lengths */
  this.nlen = 0;              /* number of length code lengths */
  this.ndist = 0;             /* number of distance code lengths */
  this.have = 0;              /* number of code lengths in lens[] */
  this.next = null;              /* next available space in codes[] */

  this.lens = new utils.Buf16(320); /* temporary storage for code lengths */
  this.work = new utils.Buf16(288); /* work area for code table building */

  /*
   because we don't have pointers in js, we use lencode and distcode directly
   as buffers so we don't need codes
  */
  //this.codes = new utils.Buf32(ENOUGH);       /* space for code tables */
  this.lendyn = null;              /* dynamic table for length/literal codes (JS specific) */
  this.distdyn = null;             /* dynamic table for distance codes (JS specific) */
  this.sane = 0;                   /* if false, allow invalid distance too far */
  this.back = 0;                   /* bits back of last unprocessed length/lit */
  this.was = 0;                    /* initial length of match */
}

function inflateResetKeep(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  strm.total_in = strm.total_out = state.total = 0;
  strm.msg = ''; /*Z_NULL*/
  if (state.wrap) {       /* to support ill-conceived Java test suite */
    strm.adler = state.wrap & 1;
  }
  state.mode = HEAD;
  state.last = 0;
  state.havedict = 0;
  state.dmax = 32768;
  state.head = null/*Z_NULL*/;
  state.hold = 0;
  state.bits = 0;
  //state.lencode = state.distcode = state.next = state.codes;
  state.lencode = state.lendyn = new utils.Buf32(ENOUGH_LENS);
  state.distcode = state.distdyn = new utils.Buf32(ENOUGH_DISTS);

  state.sane = 1;
  state.back = -1;
  //Tracev((stderr, "inflate: reset\n"));
  return Z_OK;
}

function inflateReset(strm) {
  var state;

  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  state.wsize = 0;
  state.whave = 0;
  state.wnext = 0;
  return inflateResetKeep(strm);

}

function inflateReset2(strm, windowBits) {
  var wrap;
  var state;

  /* get the state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;

  /* extract wrap request from windowBits parameter */
  if (windowBits < 0) {
    wrap = 0;
    windowBits = -windowBits;
  }
  else {
    wrap = (windowBits >> 4) + 1;
    if (windowBits < 48) {
      windowBits &= 15;
    }
  }

  /* set number of window bits, free window if different */
  if (windowBits && (windowBits < 8 || windowBits > 15)) {
    return Z_STREAM_ERROR;
  }
  if (state.window !== null && state.wbits !== windowBits) {
    state.window = null;
  }

  /* update state and reset the rest of it */
  state.wrap = wrap;
  state.wbits = windowBits;
  return inflateReset(strm);
}

function inflateInit2(strm, windowBits) {
  var ret;
  var state;

  if (!strm) { return Z_STREAM_ERROR; }
  //strm.msg = Z_NULL;                 /* in case we return an error */

  state = new InflateState();

  //if (state === Z_NULL) return Z_MEM_ERROR;
  //Tracev((stderr, "inflate: allocated\n"));
  strm.state = state;
  state.window = null/*Z_NULL*/;
  ret = inflateReset2(strm, windowBits);
  if (ret !== Z_OK) {
    strm.state = null/*Z_NULL*/;
  }
  return ret;
}

function inflateInit(strm) {
  return inflateInit2(strm, DEF_WBITS);
}


/*
 Return state with length and distance decoding tables and index sizes set to
 fixed code decoding.  Normally this returns fixed tables from inffixed.h.
 If BUILDFIXED is defined, then instead this routine builds the tables the
 first time it's called, and returns those tables the first time and
 thereafter.  This reduces the size of the code by about 2K bytes, in
 exchange for a little execution time.  However, BUILDFIXED should not be
 used for threaded applications, since the rewriting of the tables and virgin
 may not be thread-safe.
 */
var virgin = true;

var lenfix, distfix; // We have no pointers in JS, so keep tables separate

function fixedtables(state) {
  /* build fixed huffman tables if first call (may not be thread safe) */
  if (virgin) {
    var sym;

    lenfix = new utils.Buf32(512);
    distfix = new utils.Buf32(32);

    /* literal/length table */
    sym = 0;
    while (sym < 144) { state.lens[sym++] = 8; }
    while (sym < 256) { state.lens[sym++] = 9; }
    while (sym < 280) { state.lens[sym++] = 7; }
    while (sym < 288) { state.lens[sym++] = 8; }

    inflate_table(LENS,  state.lens, 0, 288, lenfix,   0, state.work, { bits: 9 });

    /* distance table */
    sym = 0;
    while (sym < 32) { state.lens[sym++] = 5; }

    inflate_table(DISTS, state.lens, 0, 32,   distfix, 0, state.work, { bits: 5 });

    /* do this just once */
    virgin = false;
  }

  state.lencode = lenfix;
  state.lenbits = 9;
  state.distcode = distfix;
  state.distbits = 5;
}


/*
 Update the window with the last wsize (normally 32K) bytes written before
 returning.  If window does not exist yet, create it.  This is only called
 when a window is already in use, or when output has been written during this
 inflate call, but the end of the deflate stream has not been reached yet.
 It is also called to create a window for dictionary data when a dictionary
 is loaded.

 Providing output buffers larger than 32K to inflate() should provide a speed
 advantage, since only the last 32K of output is copied to the sliding window
 upon return from inflate(), and since all distances after the first 32K of
 output will fall in the output data, making match copies simpler and faster.
 The advantage may be dependent on the size of the processor's data caches.
 */
function updatewindow(strm, src, end, copy) {
  var dist;
  var state = strm.state;

  /* if it hasn't been done already, allocate space for the window */
  if (state.window === null) {
    state.wsize = 1 << state.wbits;
    state.wnext = 0;
    state.whave = 0;

    state.window = new utils.Buf8(state.wsize);
  }

  /* copy state->wsize or less output bytes into the circular window */
  if (copy >= state.wsize) {
    utils.arraySet(state.window, src, end - state.wsize, state.wsize, 0);
    state.wnext = 0;
    state.whave = state.wsize;
  }
  else {
    dist = state.wsize - state.wnext;
    if (dist > copy) {
      dist = copy;
    }
    //zmemcpy(state->window + state->wnext, end - copy, dist);
    utils.arraySet(state.window, src, end - copy, dist, state.wnext);
    copy -= dist;
    if (copy) {
      //zmemcpy(state->window, end - copy, copy);
      utils.arraySet(state.window, src, end - copy, copy, 0);
      state.wnext = copy;
      state.whave = state.wsize;
    }
    else {
      state.wnext += dist;
      if (state.wnext === state.wsize) { state.wnext = 0; }
      if (state.whave < state.wsize) { state.whave += dist; }
    }
  }
  return 0;
}

function inflate(strm, flush) {
  var state;
  var input, output;          // input/output buffers
  var next;                   /* next input INDEX */
  var put;                    /* next output INDEX */
  var have, left;             /* available input and output */
  var hold;                   /* bit buffer */
  var bits;                   /* bits in bit buffer */
  var _in, _out;              /* save starting available input and output */
  var copy;                   /* number of stored or match bytes to copy */
  var from;                   /* where to copy match bytes from */
  var from_source;
  var here = 0;               /* current decoding table entry */
  var here_bits, here_op, here_val; // paked "here" denormalized (JS specific)
  //var last;                   /* parent table entry */
  var last_bits, last_op, last_val; // paked "last" denormalized (JS specific)
  var len;                    /* length to copy for repeats, bits to drop */
  var ret;                    /* return code */
  var hbuf = new utils.Buf8(4);    /* buffer for gzip header crc calculation */
  var opts;

  var n; // temporary var for NEED_BITS

  var order = /* permutation of code lengths */
    [ 16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15 ];


  if (!strm || !strm.state || !strm.output ||
      (!strm.input && strm.avail_in !== 0)) {
    return Z_STREAM_ERROR;
  }

  state = strm.state;
  if (state.mode === TYPE) { state.mode = TYPEDO; }    /* skip check */


  //--- LOAD() ---
  put = strm.next_out;
  output = strm.output;
  left = strm.avail_out;
  next = strm.next_in;
  input = strm.input;
  have = strm.avail_in;
  hold = state.hold;
  bits = state.bits;
  //---

  _in = have;
  _out = left;
  ret = Z_OK;

  inf_leave: // goto emulation
  for (;;) {
    switch (state.mode) {
    case HEAD:
      if (state.wrap === 0) {
        state.mode = TYPEDO;
        break;
      }
      //=== NEEDBITS(16);
      while (bits < 16) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      if ((state.wrap & 2) && hold === 0x8b1f) {  /* gzip header */
        state.check = 0/*crc32(0L, Z_NULL, 0)*/;
        //=== CRC2(state.check, hold);
        hbuf[0] = hold & 0xff;
        hbuf[1] = (hold >>> 8) & 0xff;
        state.check = crc32(state.check, hbuf, 2, 0);
        //===//

        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        state.mode = FLAGS;
        break;
      }
      state.flags = 0;           /* expect zlib header */
      if (state.head) {
        state.head.done = false;
      }
      if (!(state.wrap & 1) ||   /* check if zlib header allowed */
        (((hold & 0xff)/*BITS(8)*/ << 8) + (hold >> 8)) % 31) {
        strm.msg = 'incorrect header check';
        state.mode = BAD;
        break;
      }
      if ((hold & 0x0f)/*BITS(4)*/ !== Z_DEFLATED) {
        strm.msg = 'unknown compression method';
        state.mode = BAD;
        break;
      }
      //--- DROPBITS(4) ---//
      hold >>>= 4;
      bits -= 4;
      //---//
      len = (hold & 0x0f)/*BITS(4)*/ + 8;
      if (state.wbits === 0) {
        state.wbits = len;
      }
      else if (len > state.wbits) {
        strm.msg = 'invalid window size';
        state.mode = BAD;
        break;
      }
      state.dmax = 1 << len;
      //Tracev((stderr, "inflate:   zlib header ok\n"));
      strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
      state.mode = hold & 0x200 ? DICTID : TYPE;
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      break;
    case FLAGS:
      //=== NEEDBITS(16); */
      while (bits < 16) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      state.flags = hold;
      if ((state.flags & 0xff) !== Z_DEFLATED) {
        strm.msg = 'unknown compression method';
        state.mode = BAD;
        break;
      }
      if (state.flags & 0xe000) {
        strm.msg = 'unknown header flags set';
        state.mode = BAD;
        break;
      }
      if (state.head) {
        state.head.text = ((hold >> 8) & 1);
      }
      if (state.flags & 0x0200) {
        //=== CRC2(state.check, hold);
        hbuf[0] = hold & 0xff;
        hbuf[1] = (hold >>> 8) & 0xff;
        state.check = crc32(state.check, hbuf, 2, 0);
        //===//
      }
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      state.mode = TIME;
      /* falls through */
    case TIME:
      //=== NEEDBITS(32); */
      while (bits < 32) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      if (state.head) {
        state.head.time = hold;
      }
      if (state.flags & 0x0200) {
        //=== CRC4(state.check, hold)
        hbuf[0] = hold & 0xff;
        hbuf[1] = (hold >>> 8) & 0xff;
        hbuf[2] = (hold >>> 16) & 0xff;
        hbuf[3] = (hold >>> 24) & 0xff;
        state.check = crc32(state.check, hbuf, 4, 0);
        //===
      }
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      state.mode = OS;
      /* falls through */
    case OS:
      //=== NEEDBITS(16); */
      while (bits < 16) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      if (state.head) {
        state.head.xflags = (hold & 0xff);
        state.head.os = (hold >> 8);
      }
      if (state.flags & 0x0200) {
        //=== CRC2(state.check, hold);
        hbuf[0] = hold & 0xff;
        hbuf[1] = (hold >>> 8) & 0xff;
        state.check = crc32(state.check, hbuf, 2, 0);
        //===//
      }
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      state.mode = EXLEN;
      /* falls through */
    case EXLEN:
      if (state.flags & 0x0400) {
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.length = hold;
        if (state.head) {
          state.head.extra_len = hold;
        }
        if (state.flags & 0x0200) {
          //=== CRC2(state.check, hold);
          hbuf[0] = hold & 0xff;
          hbuf[1] = (hold >>> 8) & 0xff;
          state.check = crc32(state.check, hbuf, 2, 0);
          //===//
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
      }
      else if (state.head) {
        state.head.extra = null/*Z_NULL*/;
      }
      state.mode = EXTRA;
      /* falls through */
    case EXTRA:
      if (state.flags & 0x0400) {
        copy = state.length;
        if (copy > have) { copy = have; }
        if (copy) {
          if (state.head) {
            len = state.head.extra_len - state.length;
            if (!state.head.extra) {
              // Use untyped array for more conveniend processing later
              state.head.extra = new Array(state.head.extra_len);
            }
            utils.arraySet(
              state.head.extra,
              input,
              next,
              // extra field is limited to 65536 bytes
              // - no need for additional size check
              copy,
              /*len + copy > state.head.extra_max - len ? state.head.extra_max : copy,*/
              len
            );
            //zmemcpy(state.head.extra + len, next,
            //        len + copy > state.head.extra_max ?
            //        state.head.extra_max - len : copy);
          }
          if (state.flags & 0x0200) {
            state.check = crc32(state.check, input, copy, next);
          }
          have -= copy;
          next += copy;
          state.length -= copy;
        }
        if (state.length) { break inf_leave; }
      }
      state.length = 0;
      state.mode = NAME;
      /* falls through */
    case NAME:
      if (state.flags & 0x0800) {
        if (have === 0) { break inf_leave; }
        copy = 0;
        do {
          // TODO: 2 or 1 bytes?
          len = input[next + copy++];
          /* use constant limit because in js we should not preallocate memory */
          if (state.head && len &&
              (state.length < 65536 /*state.head.name_max*/)) {
            state.head.name += String.fromCharCode(len);
          }
        } while (len && copy < have);

        if (state.flags & 0x0200) {
          state.check = crc32(state.check, input, copy, next);
        }
        have -= copy;
        next += copy;
        if (len) { break inf_leave; }
      }
      else if (state.head) {
        state.head.name = null;
      }
      state.length = 0;
      state.mode = COMMENT;
      /* falls through */
    case COMMENT:
      if (state.flags & 0x1000) {
        if (have === 0) { break inf_leave; }
        copy = 0;
        do {
          len = input[next + copy++];
          /* use constant limit because in js we should not preallocate memory */
          if (state.head && len &&
              (state.length < 65536 /*state.head.comm_max*/)) {
            state.head.comment += String.fromCharCode(len);
          }
        } while (len && copy < have);
        if (state.flags & 0x0200) {
          state.check = crc32(state.check, input, copy, next);
        }
        have -= copy;
        next += copy;
        if (len) { break inf_leave; }
      }
      else if (state.head) {
        state.head.comment = null;
      }
      state.mode = HCRC;
      /* falls through */
    case HCRC:
      if (state.flags & 0x0200) {
        //=== NEEDBITS(16); */
        while (bits < 16) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (hold !== (state.check & 0xffff)) {
          strm.msg = 'header crc mismatch';
          state.mode = BAD;
          break;
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
      }
      if (state.head) {
        state.head.hcrc = ((state.flags >> 9) & 1);
        state.head.done = true;
      }
      strm.adler = state.check = 0;
      state.mode = TYPE;
      break;
    case DICTID:
      //=== NEEDBITS(32); */
      while (bits < 32) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      strm.adler = state.check = zswap32(hold);
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      state.mode = DICT;
      /* falls through */
    case DICT:
      if (state.havedict === 0) {
        //--- RESTORE() ---
        strm.next_out = put;
        strm.avail_out = left;
        strm.next_in = next;
        strm.avail_in = have;
        state.hold = hold;
        state.bits = bits;
        //---
        return Z_NEED_DICT;
      }
      strm.adler = state.check = 1/*adler32(0L, Z_NULL, 0)*/;
      state.mode = TYPE;
      /* falls through */
    case TYPE:
      if (flush === Z_BLOCK || flush === Z_TREES) { break inf_leave; }
      /* falls through */
    case TYPEDO:
      if (state.last) {
        //--- BYTEBITS() ---//
        hold >>>= bits & 7;
        bits -= bits & 7;
        //---//
        state.mode = CHECK;
        break;
      }
      //=== NEEDBITS(3); */
      while (bits < 3) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      state.last = (hold & 0x01)/*BITS(1)*/;
      //--- DROPBITS(1) ---//
      hold >>>= 1;
      bits -= 1;
      //---//

      switch ((hold & 0x03)/*BITS(2)*/) {
      case 0:                             /* stored block */
        //Tracev((stderr, "inflate:     stored block%s\n",
        //        state.last ? " (last)" : ""));
        state.mode = STORED;
        break;
      case 1:                             /* fixed block */
        fixedtables(state);
        //Tracev((stderr, "inflate:     fixed codes block%s\n",
        //        state.last ? " (last)" : ""));
        state.mode = LEN_;             /* decode codes */
        if (flush === Z_TREES) {
          //--- DROPBITS(2) ---//
          hold >>>= 2;
          bits -= 2;
          //---//
          break inf_leave;
        }
        break;
      case 2:                             /* dynamic block */
        //Tracev((stderr, "inflate:     dynamic codes block%s\n",
        //        state.last ? " (last)" : ""));
        state.mode = TABLE;
        break;
      case 3:
        strm.msg = 'invalid block type';
        state.mode = BAD;
      }
      //--- DROPBITS(2) ---//
      hold >>>= 2;
      bits -= 2;
      //---//
      break;
    case STORED:
      //--- BYTEBITS() ---// /* go to byte boundary */
      hold >>>= bits & 7;
      bits -= bits & 7;
      //---//
      //=== NEEDBITS(32); */
      while (bits < 32) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      if ((hold & 0xffff) !== ((hold >>> 16) ^ 0xffff)) {
        strm.msg = 'invalid stored block lengths';
        state.mode = BAD;
        break;
      }
      state.length = hold & 0xffff;
      //Tracev((stderr, "inflate:       stored length %u\n",
      //        state.length));
      //=== INITBITS();
      hold = 0;
      bits = 0;
      //===//
      state.mode = COPY_;
      if (flush === Z_TREES) { break inf_leave; }
      /* falls through */
    case COPY_:
      state.mode = COPY;
      /* falls through */
    case COPY:
      copy = state.length;
      if (copy) {
        if (copy > have) { copy = have; }
        if (copy > left) { copy = left; }
        if (copy === 0) { break inf_leave; }
        //--- zmemcpy(put, next, copy); ---
        utils.arraySet(output, input, next, copy, put);
        //---//
        have -= copy;
        next += copy;
        left -= copy;
        put += copy;
        state.length -= copy;
        break;
      }
      //Tracev((stderr, "inflate:       stored end\n"));
      state.mode = TYPE;
      break;
    case TABLE:
      //=== NEEDBITS(14); */
      while (bits < 14) {
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
      }
      //===//
      state.nlen = (hold & 0x1f)/*BITS(5)*/ + 257;
      //--- DROPBITS(5) ---//
      hold >>>= 5;
      bits -= 5;
      //---//
      state.ndist = (hold & 0x1f)/*BITS(5)*/ + 1;
      //--- DROPBITS(5) ---//
      hold >>>= 5;
      bits -= 5;
      //---//
      state.ncode = (hold & 0x0f)/*BITS(4)*/ + 4;
      //--- DROPBITS(4) ---//
      hold >>>= 4;
      bits -= 4;
      //---//
//#ifndef PKZIP_BUG_WORKAROUND
      if (state.nlen > 286 || state.ndist > 30) {
        strm.msg = 'too many length or distance symbols';
        state.mode = BAD;
        break;
      }
//#endif
      //Tracev((stderr, "inflate:       table sizes ok\n"));
      state.have = 0;
      state.mode = LENLENS;
      /* falls through */
    case LENLENS:
      while (state.have < state.ncode) {
        //=== NEEDBITS(3);
        while (bits < 3) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.lens[order[state.have++]] = (hold & 0x07);//BITS(3);
        //--- DROPBITS(3) ---//
        hold >>>= 3;
        bits -= 3;
        //---//
      }
      while (state.have < 19) {
        state.lens[order[state.have++]] = 0;
      }
      // We have separate tables & no pointers. 2 commented lines below not needed.
      //state.next = state.codes;
      //state.lencode = state.next;
      // Switch to use dynamic table
      state.lencode = state.lendyn;
      state.lenbits = 7;

      opts = { bits: state.lenbits };
      ret = inflate_table(CODES, state.lens, 0, 19, state.lencode, 0, state.work, opts);
      state.lenbits = opts.bits;

      if (ret) {
        strm.msg = 'invalid code lengths set';
        state.mode = BAD;
        break;
      }
      //Tracev((stderr, "inflate:       code lengths ok\n"));
      state.have = 0;
      state.mode = CODELENS;
      /* falls through */
    case CODELENS:
      while (state.have < state.nlen + state.ndist) {
        for (;;) {
          here = state.lencode[hold & ((1 << state.lenbits) - 1)];/*BITS(state.lenbits)*/
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if ((here_bits) <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        if (here_val < 16) {
          //--- DROPBITS(here.bits) ---//
          hold >>>= here_bits;
          bits -= here_bits;
          //---//
          state.lens[state.have++] = here_val;
        }
        else {
          if (here_val === 16) {
            //=== NEEDBITS(here.bits + 2);
            n = here_bits + 2;
            while (bits < n) {
              if (have === 0) { break inf_leave; }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            //--- DROPBITS(here.bits) ---//
            hold >>>= here_bits;
            bits -= here_bits;
            //---//
            if (state.have === 0) {
              strm.msg = 'invalid bit length repeat';
              state.mode = BAD;
              break;
            }
            len = state.lens[state.have - 1];
            copy = 3 + (hold & 0x03);//BITS(2);
            //--- DROPBITS(2) ---//
            hold >>>= 2;
            bits -= 2;
            //---//
          }
          else if (here_val === 17) {
            //=== NEEDBITS(here.bits + 3);
            n = here_bits + 3;
            while (bits < n) {
              if (have === 0) { break inf_leave; }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            //--- DROPBITS(here.bits) ---//
            hold >>>= here_bits;
            bits -= here_bits;
            //---//
            len = 0;
            copy = 3 + (hold & 0x07);//BITS(3);
            //--- DROPBITS(3) ---//
            hold >>>= 3;
            bits -= 3;
            //---//
          }
          else {
            //=== NEEDBITS(here.bits + 7);
            n = here_bits + 7;
            while (bits < n) {
              if (have === 0) { break inf_leave; }
              have--;
              hold += input[next++] << bits;
              bits += 8;
            }
            //===//
            //--- DROPBITS(here.bits) ---//
            hold >>>= here_bits;
            bits -= here_bits;
            //---//
            len = 0;
            copy = 11 + (hold & 0x7f);//BITS(7);
            //--- DROPBITS(7) ---//
            hold >>>= 7;
            bits -= 7;
            //---//
          }
          if (state.have + copy > state.nlen + state.ndist) {
            strm.msg = 'invalid bit length repeat';
            state.mode = BAD;
            break;
          }
          while (copy--) {
            state.lens[state.have++] = len;
          }
        }
      }

      /* handle error breaks in while */
      if (state.mode === BAD) { break; }

      /* check for end-of-block code (better have one) */
      if (state.lens[256] === 0) {
        strm.msg = 'invalid code -- missing end-of-block';
        state.mode = BAD;
        break;
      }

      /* build code tables -- note: do not change the lenbits or distbits
         values here (9 and 6) without reading the comments in inftrees.h
         concerning the ENOUGH constants, which depend on those values */
      state.lenbits = 9;

      opts = { bits: state.lenbits };
      ret = inflate_table(LENS, state.lens, 0, state.nlen, state.lencode, 0, state.work, opts);
      // We have separate tables & no pointers. 2 commented lines below not needed.
      // state.next_index = opts.table_index;
      state.lenbits = opts.bits;
      // state.lencode = state.next;

      if (ret) {
        strm.msg = 'invalid literal/lengths set';
        state.mode = BAD;
        break;
      }

      state.distbits = 6;
      //state.distcode.copy(state.codes);
      // Switch to use dynamic table
      state.distcode = state.distdyn;
      opts = { bits: state.distbits };
      ret = inflate_table(DISTS, state.lens, state.nlen, state.ndist, state.distcode, 0, state.work, opts);
      // We have separate tables & no pointers. 2 commented lines below not needed.
      // state.next_index = opts.table_index;
      state.distbits = opts.bits;
      // state.distcode = state.next;

      if (ret) {
        strm.msg = 'invalid distances set';
        state.mode = BAD;
        break;
      }
      //Tracev((stderr, 'inflate:       codes ok\n'));
      state.mode = LEN_;
      if (flush === Z_TREES) { break inf_leave; }
      /* falls through */
    case LEN_:
      state.mode = LEN;
      /* falls through */
    case LEN:
      if (have >= 6 && left >= 258) {
        //--- RESTORE() ---
        strm.next_out = put;
        strm.avail_out = left;
        strm.next_in = next;
        strm.avail_in = have;
        state.hold = hold;
        state.bits = bits;
        //---
        inflate_fast(strm, _out);
        //--- LOAD() ---
        put = strm.next_out;
        output = strm.output;
        left = strm.avail_out;
        next = strm.next_in;
        input = strm.input;
        have = strm.avail_in;
        hold = state.hold;
        bits = state.bits;
        //---

        if (state.mode === TYPE) {
          state.back = -1;
        }
        break;
      }
      state.back = 0;
      for (;;) {
        here = state.lencode[hold & ((1 << state.lenbits) - 1)];  /*BITS(state.lenbits)*/
        here_bits = here >>> 24;
        here_op = (here >>> 16) & 0xff;
        here_val = here & 0xffff;

        if (here_bits <= bits) { break; }
        //--- PULLBYTE() ---//
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
        //---//
      }
      if (here_op && (here_op & 0xf0) === 0) {
        last_bits = here_bits;
        last_op = here_op;
        last_val = here_val;
        for (;;) {
          here = state.lencode[last_val +
                  ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if ((last_bits + here_bits) <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        //--- DROPBITS(last.bits) ---//
        hold >>>= last_bits;
        bits -= last_bits;
        //---//
        state.back += last_bits;
      }
      //--- DROPBITS(here.bits) ---//
      hold >>>= here_bits;
      bits -= here_bits;
      //---//
      state.back += here_bits;
      state.length = here_val;
      if (here_op === 0) {
        //Tracevv((stderr, here.val >= 0x20 && here.val < 0x7f ?
        //        "inflate:         literal '%c'\n" :
        //        "inflate:         literal 0x%02x\n", here.val));
        state.mode = LIT;
        break;
      }
      if (here_op & 32) {
        //Tracevv((stderr, "inflate:         end of block\n"));
        state.back = -1;
        state.mode = TYPE;
        break;
      }
      if (here_op & 64) {
        strm.msg = 'invalid literal/length code';
        state.mode = BAD;
        break;
      }
      state.extra = here_op & 15;
      state.mode = LENEXT;
      /* falls through */
    case LENEXT:
      if (state.extra) {
        //=== NEEDBITS(state.extra);
        n = state.extra;
        while (bits < n) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.length += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
        //--- DROPBITS(state.extra) ---//
        hold >>>= state.extra;
        bits -= state.extra;
        //---//
        state.back += state.extra;
      }
      //Tracevv((stderr, "inflate:         length %u\n", state.length));
      state.was = state.length;
      state.mode = DIST;
      /* falls through */
    case DIST:
      for (;;) {
        here = state.distcode[hold & ((1 << state.distbits) - 1)];/*BITS(state.distbits)*/
        here_bits = here >>> 24;
        here_op = (here >>> 16) & 0xff;
        here_val = here & 0xffff;

        if ((here_bits) <= bits) { break; }
        //--- PULLBYTE() ---//
        if (have === 0) { break inf_leave; }
        have--;
        hold += input[next++] << bits;
        bits += 8;
        //---//
      }
      if ((here_op & 0xf0) === 0) {
        last_bits = here_bits;
        last_op = here_op;
        last_val = here_val;
        for (;;) {
          here = state.distcode[last_val +
                  ((hold & ((1 << (last_bits + last_op)) - 1))/*BITS(last.bits + last.op)*/ >> last_bits)];
          here_bits = here >>> 24;
          here_op = (here >>> 16) & 0xff;
          here_val = here & 0xffff;

          if ((last_bits + here_bits) <= bits) { break; }
          //--- PULLBYTE() ---//
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
          //---//
        }
        //--- DROPBITS(last.bits) ---//
        hold >>>= last_bits;
        bits -= last_bits;
        //---//
        state.back += last_bits;
      }
      //--- DROPBITS(here.bits) ---//
      hold >>>= here_bits;
      bits -= here_bits;
      //---//
      state.back += here_bits;
      if (here_op & 64) {
        strm.msg = 'invalid distance code';
        state.mode = BAD;
        break;
      }
      state.offset = here_val;
      state.extra = (here_op) & 15;
      state.mode = DISTEXT;
      /* falls through */
    case DISTEXT:
      if (state.extra) {
        //=== NEEDBITS(state.extra);
        n = state.extra;
        while (bits < n) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        state.offset += hold & ((1 << state.extra) - 1)/*BITS(state.extra)*/;
        //--- DROPBITS(state.extra) ---//
        hold >>>= state.extra;
        bits -= state.extra;
        //---//
        state.back += state.extra;
      }
//#ifdef INFLATE_STRICT
      if (state.offset > state.dmax) {
        strm.msg = 'invalid distance too far back';
        state.mode = BAD;
        break;
      }
//#endif
      //Tracevv((stderr, "inflate:         distance %u\n", state.offset));
      state.mode = MATCH;
      /* falls through */
    case MATCH:
      if (left === 0) { break inf_leave; }
      copy = _out - left;
      if (state.offset > copy) {         /* copy from window */
        copy = state.offset - copy;
        if (copy > state.whave) {
          if (state.sane) {
            strm.msg = 'invalid distance too far back';
            state.mode = BAD;
            break;
          }
// (!) This block is disabled in zlib defailts,
// don't enable it for binary compatibility
//#ifdef INFLATE_ALLOW_INVALID_DISTANCE_TOOFAR_ARRR
//          Trace((stderr, "inflate.c too far\n"));
//          copy -= state.whave;
//          if (copy > state.length) { copy = state.length; }
//          if (copy > left) { copy = left; }
//          left -= copy;
//          state.length -= copy;
//          do {
//            output[put++] = 0;
//          } while (--copy);
//          if (state.length === 0) { state.mode = LEN; }
//          break;
//#endif
        }
        if (copy > state.wnext) {
          copy -= state.wnext;
          from = state.wsize - copy;
        }
        else {
          from = state.wnext - copy;
        }
        if (copy > state.length) { copy = state.length; }
        from_source = state.window;
      }
      else {                              /* copy from output */
        from_source = output;
        from = put - state.offset;
        copy = state.length;
      }
      if (copy > left) { copy = left; }
      left -= copy;
      state.length -= copy;
      do {
        output[put++] = from_source[from++];
      } while (--copy);
      if (state.length === 0) { state.mode = LEN; }
      break;
    case LIT:
      if (left === 0) { break inf_leave; }
      output[put++] = state.length;
      left--;
      state.mode = LEN;
      break;
    case CHECK:
      if (state.wrap) {
        //=== NEEDBITS(32);
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          // Use '|' insdead of '+' to make sure that result is signed
          hold |= input[next++] << bits;
          bits += 8;
        }
        //===//
        _out -= left;
        strm.total_out += _out;
        state.total += _out;
        if (_out) {
          strm.adler = state.check =
              /*UPDATE(state.check, put - _out, _out);*/
              (state.flags ? crc32(state.check, output, _out, put - _out) : adler32(state.check, output, _out, put - _out));

        }
        _out = left;
        // NB: crc32 stored as signed 32-bit int, zswap32 returns signed too
        if ((state.flags ? hold : zswap32(hold)) !== state.check) {
          strm.msg = 'incorrect data check';
          state.mode = BAD;
          break;
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        //Tracev((stderr, "inflate:   check matches trailer\n"));
      }
      state.mode = LENGTH;
      /* falls through */
    case LENGTH:
      if (state.wrap && state.flags) {
        //=== NEEDBITS(32);
        while (bits < 32) {
          if (have === 0) { break inf_leave; }
          have--;
          hold += input[next++] << bits;
          bits += 8;
        }
        //===//
        if (hold !== (state.total & 0xffffffff)) {
          strm.msg = 'incorrect length check';
          state.mode = BAD;
          break;
        }
        //=== INITBITS();
        hold = 0;
        bits = 0;
        //===//
        //Tracev((stderr, "inflate:   length matches trailer\n"));
      }
      state.mode = DONE;
      /* falls through */
    case DONE:
      ret = Z_STREAM_END;
      break inf_leave;
    case BAD:
      ret = Z_DATA_ERROR;
      break inf_leave;
    case MEM:
      return Z_MEM_ERROR;
    case SYNC:
      /* falls through */
    default:
      return Z_STREAM_ERROR;
    }
  }

  // inf_leave <- here is real place for "goto inf_leave", emulated via "break inf_leave"

  /*
     Return from inflate(), updating the total counts and the check value.
     If there was no progress during the inflate() call, return a buffer
     error.  Call updatewindow() to create and/or update the window state.
     Note: a memory error from inflate() is non-recoverable.
   */

  //--- RESTORE() ---
  strm.next_out = put;
  strm.avail_out = left;
  strm.next_in = next;
  strm.avail_in = have;
  state.hold = hold;
  state.bits = bits;
  //---

  if (state.wsize || (_out !== strm.avail_out && state.mode < BAD &&
                      (state.mode < CHECK || flush !== Z_FINISH))) {
    if (updatewindow(strm, strm.output, strm.next_out, _out - strm.avail_out)) {
      state.mode = MEM;
      return Z_MEM_ERROR;
    }
  }
  _in -= strm.avail_in;
  _out -= strm.avail_out;
  strm.total_in += _in;
  strm.total_out += _out;
  state.total += _out;
  if (state.wrap && _out) {
    strm.adler = state.check = /*UPDATE(state.check, strm.next_out - _out, _out);*/
      (state.flags ? crc32(state.check, output, _out, strm.next_out - _out) : adler32(state.check, output, _out, strm.next_out - _out));
  }
  strm.data_type = state.bits + (state.last ? 64 : 0) +
                    (state.mode === TYPE ? 128 : 0) +
                    (state.mode === LEN_ || state.mode === COPY_ ? 256 : 0);
  if (((_in === 0 && _out === 0) || flush === Z_FINISH) && ret === Z_OK) {
    ret = Z_BUF_ERROR;
  }
  return ret;
}

function inflateEnd(strm) {

  if (!strm || !strm.state /*|| strm->zfree == (free_func)0*/) {
    return Z_STREAM_ERROR;
  }

  var state = strm.state;
  if (state.window) {
    state.window = null;
  }
  strm.state = null;
  return Z_OK;
}

function inflateGetHeader(strm, head) {
  var state;

  /* check state */
  if (!strm || !strm.state) { return Z_STREAM_ERROR; }
  state = strm.state;
  if ((state.wrap & 2) === 0) { return Z_STREAM_ERROR; }

  /* save header structure */
  state.head = head;
  head.done = false;
  return Z_OK;
}

function inflateSetDictionary(strm, dictionary) {
  var dictLength = dictionary.length;

  var state;
  var dictid;
  var ret;

  /* check state */
  if (!strm /* == Z_NULL */ || !strm.state /* == Z_NULL */) { return Z_STREAM_ERROR; }
  state = strm.state;

  if (state.wrap !== 0 && state.mode !== DICT) {
    return Z_STREAM_ERROR;
  }

  /* check for correct dictionary identifier */
  if (state.mode === DICT) {
    dictid = 1; /* adler32(0, null, 0)*/
    /* dictid = adler32(dictid, dictionary, dictLength); */
    dictid = adler32(dictid, dictionary, dictLength, 0);
    if (dictid !== state.check) {
      return Z_DATA_ERROR;
    }
  }
  /* copy dictionary to window using updatewindow(), which will amend the
   existing dictionary if appropriate */
  ret = updatewindow(strm, dictionary, dictLength, dictLength);
  if (ret) {
    state.mode = MEM;
    return Z_MEM_ERROR;
  }
  state.havedict = 1;
  // Tracev((stderr, "inflate:   dictionary set\n"));
  return Z_OK;
}

exports.inflateReset = inflateReset;
exports.inflateReset2 = inflateReset2;
exports.inflateResetKeep = inflateResetKeep;
exports.inflateInit = inflateInit;
exports.inflateInit2 = inflateInit2;
exports.inflate = inflate;
exports.inflateEnd = inflateEnd;
exports.inflateGetHeader = inflateGetHeader;
exports.inflateSetDictionary = inflateSetDictionary;
exports.inflateInfo = 'pako inflate (from Nodeca project)';

/* Not implemented
exports.inflateCopy = inflateCopy;
exports.inflateGetDictionary = inflateGetDictionary;
exports.inflateMark = inflateMark;
exports.inflatePrime = inflatePrime;
exports.inflateSync = inflateSync;
exports.inflateSyncPoint = inflateSyncPoint;
exports.inflateUndermine = inflateUndermine;
*/

},{"../utils/common":41,"./adler32":43,"./crc32":45,"./inffast":48,"./inftrees":50}],50:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils = require('../utils/common');

var MAXBITS = 15;
var ENOUGH_LENS = 852;
var ENOUGH_DISTS = 592;
//var ENOUGH = (ENOUGH_LENS+ENOUGH_DISTS);

var CODES = 0;
var LENS = 1;
var DISTS = 2;

var lbase = [ /* Length codes 257..285 base */
  3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31,
  35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258, 0, 0
];

var lext = [ /* Length codes 257..285 extra */
  16, 16, 16, 16, 16, 16, 16, 16, 17, 17, 17, 17, 18, 18, 18, 18,
  19, 19, 19, 19, 20, 20, 20, 20, 21, 21, 21, 21, 16, 72, 78
];

var dbase = [ /* Distance codes 0..29 base */
  1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193,
  257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145,
  8193, 12289, 16385, 24577, 0, 0
];

var dext = [ /* Distance codes 0..29 extra */
  16, 16, 16, 16, 17, 17, 18, 18, 19, 19, 20, 20, 21, 21, 22, 22,
  23, 23, 24, 24, 25, 25, 26, 26, 27, 27,
  28, 28, 29, 29, 64, 64
];

module.exports = function inflate_table(type, lens, lens_index, codes, table, table_index, work, opts)
{
  var bits = opts.bits;
      //here = opts.here; /* table entry for duplication */

  var len = 0;               /* a code's length in bits */
  var sym = 0;               /* index of code symbols */
  var min = 0, max = 0;          /* minimum and maximum code lengths */
  var root = 0;              /* number of index bits for root table */
  var curr = 0;              /* number of index bits for current table */
  var drop = 0;              /* code bits to drop for sub-table */
  var left = 0;                   /* number of prefix codes available */
  var used = 0;              /* code entries in table used */
  var huff = 0;              /* Huffman code */
  var incr;              /* for incrementing code, index */
  var fill;              /* index for replicating entries */
  var low;               /* low bits for current root entry */
  var mask;              /* mask for low root bits */
  var next;             /* next available space in table */
  var base = null;     /* base value table to use */
  var base_index = 0;
//  var shoextra;    /* extra bits table to use */
  var end;                    /* use base and extra for symbol > end */
  var count = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];    /* number of codes of each length */
  var offs = new utils.Buf16(MAXBITS + 1); //[MAXBITS+1];     /* offsets in table for each length */
  var extra = null;
  var extra_index = 0;

  var here_bits, here_op, here_val;

  /*
   Process a set of code lengths to create a canonical Huffman code.  The
   code lengths are lens[0..codes-1].  Each length corresponds to the
   symbols 0..codes-1.  The Huffman code is generated by first sorting the
   symbols by length from short to long, and retaining the symbol order
   for codes with equal lengths.  Then the code starts with all zero bits
   for the first code of the shortest length, and the codes are integer
   increments for the same length, and zeros are appended as the length
   increases.  For the deflate format, these bits are stored backwards
   from their more natural integer increment ordering, and so when the
   decoding tables are built in the large loop below, the integer codes
   are incremented backwards.

   This routine assumes, but does not check, that all of the entries in
   lens[] are in the range 0..MAXBITS.  The caller must assure this.
   1..MAXBITS is interpreted as that code length.  zero means that that
   symbol does not occur in this code.

   The codes are sorted by computing a count of codes for each length,
   creating from that a table of starting indices for each length in the
   sorted table, and then entering the symbols in order in the sorted
   table.  The sorted table is work[], with that space being provided by
   the caller.

   The length counts are used for other purposes as well, i.e. finding
   the minimum and maximum length codes, determining if there are any
   codes at all, checking for a valid set of lengths, and looking ahead
   at length counts to determine sub-table sizes when building the
   decoding tables.
   */

  /* accumulate lengths for codes (assumes lens[] all in 0..MAXBITS) */
  for (len = 0; len <= MAXBITS; len++) {
    count[len] = 0;
  }
  for (sym = 0; sym < codes; sym++) {
    count[lens[lens_index + sym]]++;
  }

  /* bound code lengths, force root to be within code lengths */
  root = bits;
  for (max = MAXBITS; max >= 1; max--) {
    if (count[max] !== 0) { break; }
  }
  if (root > max) {
    root = max;
  }
  if (max === 0) {                     /* no symbols to code at all */
    //table.op[opts.table_index] = 64;  //here.op = (var char)64;    /* invalid code marker */
    //table.bits[opts.table_index] = 1;   //here.bits = (var char)1;
    //table.val[opts.table_index++] = 0;   //here.val = (var short)0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;


    //table.op[opts.table_index] = 64;
    //table.bits[opts.table_index] = 1;
    //table.val[opts.table_index++] = 0;
    table[table_index++] = (1 << 24) | (64 << 16) | 0;

    opts.bits = 1;
    return 0;     /* no symbols, but wait for decoding to report error */
  }
  for (min = 1; min < max; min++) {
    if (count[min] !== 0) { break; }
  }
  if (root < min) {
    root = min;
  }

  /* check for an over-subscribed or incomplete set of lengths */
  left = 1;
  for (len = 1; len <= MAXBITS; len++) {
    left <<= 1;
    left -= count[len];
    if (left < 0) {
      return -1;
    }        /* over-subscribed */
  }
  if (left > 0 && (type === CODES || max !== 1)) {
    return -1;                      /* incomplete set */
  }

  /* generate offsets into symbol table for each length for sorting */
  offs[1] = 0;
  for (len = 1; len < MAXBITS; len++) {
    offs[len + 1] = offs[len] + count[len];
  }

  /* sort symbols by length, by symbol order within each length */
  for (sym = 0; sym < codes; sym++) {
    if (lens[lens_index + sym] !== 0) {
      work[offs[lens[lens_index + sym]]++] = sym;
    }
  }

  /*
   Create and fill in decoding tables.  In this loop, the table being
   filled is at next and has curr index bits.  The code being used is huff
   with length len.  That code is converted to an index by dropping drop
   bits off of the bottom.  For codes where len is less than drop + curr,
   those top drop + curr - len bits are incremented through all values to
   fill the table with replicated entries.

   root is the number of index bits for the root table.  When len exceeds
   root, sub-tables are created pointed to by the root entry with an index
   of the low root bits of huff.  This is saved in low to check for when a
   new sub-table should be started.  drop is zero when the root table is
   being filled, and drop is root when sub-tables are being filled.

   When a new sub-table is needed, it is necessary to look ahead in the
   code lengths to determine what size sub-table is needed.  The length
   counts are used for this, and so count[] is decremented as codes are
   entered in the tables.

   used keeps track of how many table entries have been allocated from the
   provided *table space.  It is checked for LENS and DIST tables against
   the constants ENOUGH_LENS and ENOUGH_DISTS to guard against changes in
   the initial root table size constants.  See the comments in inftrees.h
   for more information.

   sym increments through all symbols, and the loop terminates when
   all codes of length max, i.e. all codes, have been processed.  This
   routine permits incomplete codes, so another loop after this one fills
   in the rest of the decoding tables with invalid code markers.
   */

  /* set up for code type */
  // poor man optimization - use if-else instead of switch,
  // to avoid deopts in old v8
  if (type === CODES) {
    base = extra = work;    /* dummy value--not used */
    end = 19;

  } else if (type === LENS) {
    base = lbase;
    base_index -= 257;
    extra = lext;
    extra_index -= 257;
    end = 256;

  } else {                    /* DISTS */
    base = dbase;
    extra = dext;
    end = -1;
  }

  /* initialize opts for loop */
  huff = 0;                   /* starting code */
  sym = 0;                    /* starting code symbol */
  len = min;                  /* starting code length */
  next = table_index;              /* current table to fill in */
  curr = root;                /* current table index bits */
  drop = 0;                   /* current bits to drop from code for index */
  low = -1;                   /* trigger new sub-table when len > root */
  used = 1 << root;          /* use root table entries */
  mask = used - 1;            /* mask for comparing low */

  /* check available table space */
  if ((type === LENS && used > ENOUGH_LENS) ||
    (type === DISTS && used > ENOUGH_DISTS)) {
    return 1;
  }

  /* process all codes and make table entries */
  for (;;) {
    /* create table entry */
    here_bits = len - drop;
    if (work[sym] < end) {
      here_op = 0;
      here_val = work[sym];
    }
    else if (work[sym] > end) {
      here_op = extra[extra_index + work[sym]];
      here_val = base[base_index + work[sym]];
    }
    else {
      here_op = 32 + 64;         /* end of block */
      here_val = 0;
    }

    /* replicate for those indices with low len bits equal to huff */
    incr = 1 << (len - drop);
    fill = 1 << curr;
    min = fill;                 /* save offset to next table */
    do {
      fill -= incr;
      table[next + (huff >> drop) + fill] = (here_bits << 24) | (here_op << 16) | here_val |0;
    } while (fill !== 0);

    /* backwards increment the len-bit code huff */
    incr = 1 << (len - 1);
    while (huff & incr) {
      incr >>= 1;
    }
    if (incr !== 0) {
      huff &= incr - 1;
      huff += incr;
    } else {
      huff = 0;
    }

    /* go to next symbol, update count, len */
    sym++;
    if (--count[len] === 0) {
      if (len === max) { break; }
      len = lens[lens_index + work[sym]];
    }

    /* create new sub-table if needed */
    if (len > root && (huff & mask) !== low) {
      /* if first time, transition to sub-tables */
      if (drop === 0) {
        drop = root;
      }

      /* increment past last table */
      next += min;            /* here min is 1 << curr */

      /* determine length of next table */
      curr = len - drop;
      left = 1 << curr;
      while (curr + drop < max) {
        left -= count[curr + drop];
        if (left <= 0) { break; }
        curr++;
        left <<= 1;
      }

      /* check for enough space */
      used += 1 << curr;
      if ((type === LENS && used > ENOUGH_LENS) ||
        (type === DISTS && used > ENOUGH_DISTS)) {
        return 1;
      }

      /* point entry in root table to sub-table */
      low = huff & mask;
      /*table.op[low] = curr;
      table.bits[low] = root;
      table.val[low] = next - opts.table_index;*/
      table[low] = (root << 24) | (curr << 16) | (next - table_index) |0;
    }
  }

  /* fill in remaining table entry if code is incomplete (guaranteed to have
   at most one remaining entry, since if the code is incomplete, the
   maximum code length that was allowed to get this far is one bit) */
  if (huff !== 0) {
    //table.op[next + huff] = 64;            /* invalid code marker */
    //table.bits[next + huff] = len - drop;
    //table.val[next + huff] = 0;
    table[next + huff] = ((len - drop) << 24) | (64 << 16) |0;
  }

  /* set return parameters */
  //opts.table_index += used;
  opts.bits = root;
  return 0;
};

},{"../utils/common":41}],51:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

module.exports = {
  2:      'need dictionary',     /* Z_NEED_DICT       2  */
  1:      'stream end',          /* Z_STREAM_END      1  */
  0:      '',                    /* Z_OK              0  */
  '-1':   'file error',          /* Z_ERRNO         (-1) */
  '-2':   'stream error',        /* Z_STREAM_ERROR  (-2) */
  '-3':   'data error',          /* Z_DATA_ERROR    (-3) */
  '-4':   'insufficient memory', /* Z_MEM_ERROR     (-4) */
  '-5':   'buffer error',        /* Z_BUF_ERROR     (-5) */
  '-6':   'incompatible version' /* Z_VERSION_ERROR (-6) */
};

},{}],52:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

var utils = require('../utils/common');

/* Public constants ==========================================================*/
/* ===========================================================================*/


//var Z_FILTERED          = 1;
//var Z_HUFFMAN_ONLY      = 2;
//var Z_RLE               = 3;
var Z_FIXED               = 4;
//var Z_DEFAULT_STRATEGY  = 0;

/* Possible values of the data_type field (though see inflate()) */
var Z_BINARY              = 0;
var Z_TEXT                = 1;
//var Z_ASCII             = 1; // = Z_TEXT
var Z_UNKNOWN             = 2;

/*============================================================================*/


function zero(buf) { var len = buf.length; while (--len >= 0) { buf[len] = 0; } }

// From zutil.h

var STORED_BLOCK = 0;
var STATIC_TREES = 1;
var DYN_TREES    = 2;
/* The three kinds of block type */

var MIN_MATCH    = 3;
var MAX_MATCH    = 258;
/* The minimum and maximum match lengths */

// From deflate.h
/* ===========================================================================
 * Internal compression state.
 */

var LENGTH_CODES  = 29;
/* number of length codes, not counting the special END_BLOCK code */

var LITERALS      = 256;
/* number of literal bytes 0..255 */

var L_CODES       = LITERALS + 1 + LENGTH_CODES;
/* number of Literal or Length codes, including the END_BLOCK code */

var D_CODES       = 30;
/* number of distance codes */

var BL_CODES      = 19;
/* number of codes used to transfer the bit lengths */

var HEAP_SIZE     = 2 * L_CODES + 1;
/* maximum heap size */

var MAX_BITS      = 15;
/* All codes must not exceed MAX_BITS bits */

var Buf_size      = 16;
/* size of bit buffer in bi_buf */


/* ===========================================================================
 * Constants
 */

var MAX_BL_BITS = 7;
/* Bit length codes must not exceed MAX_BL_BITS bits */

var END_BLOCK   = 256;
/* end of block literal code */

var REP_3_6     = 16;
/* repeat previous bit length 3-6 times (2 bits of repeat count) */

var REPZ_3_10   = 17;
/* repeat a zero length 3-10 times  (3 bits of repeat count) */

var REPZ_11_138 = 18;
/* repeat a zero length 11-138 times  (7 bits of repeat count) */

/* eslint-disable comma-spacing,array-bracket-spacing */
var extra_lbits =   /* extra bits for each length code */
  [0,0,0,0,0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,0];

var extra_dbits =   /* extra bits for each distance code */
  [0,0,0,0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,12,12,13,13];

var extra_blbits =  /* extra bits for each bit length code */
  [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,2,3,7];

var bl_order =
  [16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15];
/* eslint-enable comma-spacing,array-bracket-spacing */

/* The lengths of the bit length codes are sent in order of decreasing
 * probability, to avoid transmitting the lengths for unused bit length codes.
 */

/* ===========================================================================
 * Local data. These are initialized only once.
 */

// We pre-fill arrays with 0 to avoid uninitialized gaps

var DIST_CODE_LEN = 512; /* see definition of array dist_code below */

// !!!! Use flat array insdead of structure, Freq = i*2, Len = i*2+1
var static_ltree  = new Array((L_CODES + 2) * 2);
zero(static_ltree);
/* The static literal tree. Since the bit lengths are imposed, there is no
 * need for the L_CODES extra codes used during heap construction. However
 * The codes 286 and 287 are needed to build a canonical tree (see _tr_init
 * below).
 */

var static_dtree  = new Array(D_CODES * 2);
zero(static_dtree);
/* The static distance tree. (Actually a trivial tree since all codes use
 * 5 bits.)
 */

var _dist_code    = new Array(DIST_CODE_LEN);
zero(_dist_code);
/* Distance codes. The first 256 values correspond to the distances
 * 3 .. 258, the last 256 values correspond to the top 8 bits of
 * the 15 bit distances.
 */

var _length_code  = new Array(MAX_MATCH - MIN_MATCH + 1);
zero(_length_code);
/* length code for each normalized match length (0 == MIN_MATCH) */

var base_length   = new Array(LENGTH_CODES);
zero(base_length);
/* First normalized length for each code (0 = MIN_MATCH) */

var base_dist     = new Array(D_CODES);
zero(base_dist);
/* First normalized distance for each code (0 = distance of 1) */


function StaticTreeDesc(static_tree, extra_bits, extra_base, elems, max_length) {

  this.static_tree  = static_tree;  /* static tree or NULL */
  this.extra_bits   = extra_bits;   /* extra bits for each code or NULL */
  this.extra_base   = extra_base;   /* base index for extra_bits */
  this.elems        = elems;        /* max number of elements in the tree */
  this.max_length   = max_length;   /* max bit length for the codes */

  // show if `static_tree` has data or dummy - needed for monomorphic objects
  this.has_stree    = static_tree && static_tree.length;
}


var static_l_desc;
var static_d_desc;
var static_bl_desc;


function TreeDesc(dyn_tree, stat_desc) {
  this.dyn_tree = dyn_tree;     /* the dynamic tree */
  this.max_code = 0;            /* largest code with non zero frequency */
  this.stat_desc = stat_desc;   /* the corresponding static tree */
}



function d_code(dist) {
  return dist < 256 ? _dist_code[dist] : _dist_code[256 + (dist >>> 7)];
}


/* ===========================================================================
 * Output a short LSB first on the stream.
 * IN assertion: there is enough room in pendingBuf.
 */
function put_short(s, w) {
//    put_byte(s, (uch)((w) & 0xff));
//    put_byte(s, (uch)((ush)(w) >> 8));
  s.pending_buf[s.pending++] = (w) & 0xff;
  s.pending_buf[s.pending++] = (w >>> 8) & 0xff;
}


/* ===========================================================================
 * Send a value on a given number of bits.
 * IN assertion: length <= 16 and value fits in length bits.
 */
function send_bits(s, value, length) {
  if (s.bi_valid > (Buf_size - length)) {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    put_short(s, s.bi_buf);
    s.bi_buf = value >> (Buf_size - s.bi_valid);
    s.bi_valid += length - Buf_size;
  } else {
    s.bi_buf |= (value << s.bi_valid) & 0xffff;
    s.bi_valid += length;
  }
}


function send_code(s, c, tree) {
  send_bits(s, tree[c * 2]/*.Code*/, tree[c * 2 + 1]/*.Len*/);
}


/* ===========================================================================
 * Reverse the first len bits of a code, using straightforward code (a faster
 * method would use a table)
 * IN assertion: 1 <= len <= 15
 */
function bi_reverse(code, len) {
  var res = 0;
  do {
    res |= code & 1;
    code >>>= 1;
    res <<= 1;
  } while (--len > 0);
  return res >>> 1;
}


/* ===========================================================================
 * Flush the bit buffer, keeping at most 7 bits in it.
 */
function bi_flush(s) {
  if (s.bi_valid === 16) {
    put_short(s, s.bi_buf);
    s.bi_buf = 0;
    s.bi_valid = 0;

  } else if (s.bi_valid >= 8) {
    s.pending_buf[s.pending++] = s.bi_buf & 0xff;
    s.bi_buf >>= 8;
    s.bi_valid -= 8;
  }
}


/* ===========================================================================
 * Compute the optimal bit lengths for a tree and update the total bit length
 * for the current block.
 * IN assertion: the fields freq and dad are set, heap[heap_max] and
 *    above are the tree nodes sorted by increasing frequency.
 * OUT assertions: the field len is set to the optimal bit length, the
 *     array bl_count contains the frequencies for each bit length.
 *     The length opt_len is updated; static_len is also updated if stree is
 *     not null.
 */
function gen_bitlen(s, desc)
//    deflate_state *s;
//    tree_desc *desc;    /* the tree descriptor */
{
  var tree            = desc.dyn_tree;
  var max_code        = desc.max_code;
  var stree           = desc.stat_desc.static_tree;
  var has_stree       = desc.stat_desc.has_stree;
  var extra           = desc.stat_desc.extra_bits;
  var base            = desc.stat_desc.extra_base;
  var max_length      = desc.stat_desc.max_length;
  var h;              /* heap index */
  var n, m;           /* iterate over the tree elements */
  var bits;           /* bit length */
  var xbits;          /* extra bits */
  var f;              /* frequency */
  var overflow = 0;   /* number of elements with bit length too large */

  for (bits = 0; bits <= MAX_BITS; bits++) {
    s.bl_count[bits] = 0;
  }

  /* In a first pass, compute the optimal bit lengths (which may
   * overflow in the case of the bit length tree).
   */
  tree[s.heap[s.heap_max] * 2 + 1]/*.Len*/ = 0; /* root of the heap */

  for (h = s.heap_max + 1; h < HEAP_SIZE; h++) {
    n = s.heap[h];
    bits = tree[tree[n * 2 + 1]/*.Dad*/ * 2 + 1]/*.Len*/ + 1;
    if (bits > max_length) {
      bits = max_length;
      overflow++;
    }
    tree[n * 2 + 1]/*.Len*/ = bits;
    /* We overwrite tree[n].Dad which is no longer needed */

    if (n > max_code) { continue; } /* not a leaf node */

    s.bl_count[bits]++;
    xbits = 0;
    if (n >= base) {
      xbits = extra[n - base];
    }
    f = tree[n * 2]/*.Freq*/;
    s.opt_len += f * (bits + xbits);
    if (has_stree) {
      s.static_len += f * (stree[n * 2 + 1]/*.Len*/ + xbits);
    }
  }
  if (overflow === 0) { return; }

  // Trace((stderr,"\nbit length overflow\n"));
  /* This happens for example on obj2 and pic of the Calgary corpus */

  /* Find the first bit length which could increase: */
  do {
    bits = max_length - 1;
    while (s.bl_count[bits] === 0) { bits--; }
    s.bl_count[bits]--;      /* move one leaf down the tree */
    s.bl_count[bits + 1] += 2; /* move one overflow item as its brother */
    s.bl_count[max_length]--;
    /* The brother of the overflow item also moves one step up,
     * but this does not affect bl_count[max_length]
     */
    overflow -= 2;
  } while (overflow > 0);

  /* Now recompute all bit lengths, scanning in increasing frequency.
   * h is still equal to HEAP_SIZE. (It is simpler to reconstruct all
   * lengths instead of fixing only the wrong ones. This idea is taken
   * from 'ar' written by Haruhiko Okumura.)
   */
  for (bits = max_length; bits !== 0; bits--) {
    n = s.bl_count[bits];
    while (n !== 0) {
      m = s.heap[--h];
      if (m > max_code) { continue; }
      if (tree[m * 2 + 1]/*.Len*/ !== bits) {
        // Trace((stderr,"code %d bits %d->%d\n", m, tree[m].Len, bits));
        s.opt_len += (bits - tree[m * 2 + 1]/*.Len*/) * tree[m * 2]/*.Freq*/;
        tree[m * 2 + 1]/*.Len*/ = bits;
      }
      n--;
    }
  }
}


/* ===========================================================================
 * Generate the codes for a given tree and bit counts (which need not be
 * optimal).
 * IN assertion: the array bl_count contains the bit length statistics for
 * the given tree and the field len is set for all tree elements.
 * OUT assertion: the field code is set for all tree elements of non
 *     zero code length.
 */
function gen_codes(tree, max_code, bl_count)
//    ct_data *tree;             /* the tree to decorate */
//    int max_code;              /* largest code with non zero frequency */
//    ushf *bl_count;            /* number of codes at each bit length */
{
  var next_code = new Array(MAX_BITS + 1); /* next code value for each bit length */
  var code = 0;              /* running code value */
  var bits;                  /* bit index */
  var n;                     /* code index */

  /* The distribution counts are first used to generate the code values
   * without bit reversal.
   */
  for (bits = 1; bits <= MAX_BITS; bits++) {
    next_code[bits] = code = (code + bl_count[bits - 1]) << 1;
  }
  /* Check that the bit counts in bl_count are consistent. The last code
   * must be all ones.
   */
  //Assert (code + bl_count[MAX_BITS]-1 == (1<<MAX_BITS)-1,
  //        "inconsistent bit counts");
  //Tracev((stderr,"\ngen_codes: max_code %d ", max_code));

  for (n = 0;  n <= max_code; n++) {
    var len = tree[n * 2 + 1]/*.Len*/;
    if (len === 0) { continue; }
    /* Now reverse the bits */
    tree[n * 2]/*.Code*/ = bi_reverse(next_code[len]++, len);

    //Tracecv(tree != static_ltree, (stderr,"\nn %3d %c l %2d c %4x (%x) ",
    //     n, (isgraph(n) ? n : ' '), len, tree[n].Code, next_code[len]-1));
  }
}


/* ===========================================================================
 * Initialize the various 'constant' tables.
 */
function tr_static_init() {
  var n;        /* iterates over tree elements */
  var bits;     /* bit counter */
  var length;   /* length value */
  var code;     /* code value */
  var dist;     /* distance index */
  var bl_count = new Array(MAX_BITS + 1);
  /* number of codes at each bit length for an optimal tree */

  // do check in _tr_init()
  //if (static_init_done) return;

  /* For some embedded targets, global variables are not initialized: */
/*#ifdef NO_INIT_GLOBAL_POINTERS
  static_l_desc.static_tree = static_ltree;
  static_l_desc.extra_bits = extra_lbits;
  static_d_desc.static_tree = static_dtree;
  static_d_desc.extra_bits = extra_dbits;
  static_bl_desc.extra_bits = extra_blbits;
#endif*/

  /* Initialize the mapping length (0..255) -> length code (0..28) */
  length = 0;
  for (code = 0; code < LENGTH_CODES - 1; code++) {
    base_length[code] = length;
    for (n = 0; n < (1 << extra_lbits[code]); n++) {
      _length_code[length++] = code;
    }
  }
  //Assert (length == 256, "tr_static_init: length != 256");
  /* Note that the length 255 (match length 258) can be represented
   * in two different ways: code 284 + 5 bits or code 285, so we
   * overwrite length_code[255] to use the best encoding:
   */
  _length_code[length - 1] = code;

  /* Initialize the mapping dist (0..32K) -> dist code (0..29) */
  dist = 0;
  for (code = 0; code < 16; code++) {
    base_dist[code] = dist;
    for (n = 0; n < (1 << extra_dbits[code]); n++) {
      _dist_code[dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: dist != 256");
  dist >>= 7; /* from now on, all distances are divided by 128 */
  for (; code < D_CODES; code++) {
    base_dist[code] = dist << 7;
    for (n = 0; n < (1 << (extra_dbits[code] - 7)); n++) {
      _dist_code[256 + dist++] = code;
    }
  }
  //Assert (dist == 256, "tr_static_init: 256+dist != 512");

  /* Construct the codes of the static literal tree */
  for (bits = 0; bits <= MAX_BITS; bits++) {
    bl_count[bits] = 0;
  }

  n = 0;
  while (n <= 143) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  while (n <= 255) {
    static_ltree[n * 2 + 1]/*.Len*/ = 9;
    n++;
    bl_count[9]++;
  }
  while (n <= 279) {
    static_ltree[n * 2 + 1]/*.Len*/ = 7;
    n++;
    bl_count[7]++;
  }
  while (n <= 287) {
    static_ltree[n * 2 + 1]/*.Len*/ = 8;
    n++;
    bl_count[8]++;
  }
  /* Codes 286 and 287 do not exist, but we must include them in the
   * tree construction to get a canonical Huffman tree (longest code
   * all ones)
   */
  gen_codes(static_ltree, L_CODES + 1, bl_count);

  /* The static distance tree is trivial: */
  for (n = 0; n < D_CODES; n++) {
    static_dtree[n * 2 + 1]/*.Len*/ = 5;
    static_dtree[n * 2]/*.Code*/ = bi_reverse(n, 5);
  }

  // Now data ready and we can init static trees
  static_l_desc = new StaticTreeDesc(static_ltree, extra_lbits, LITERALS + 1, L_CODES, MAX_BITS);
  static_d_desc = new StaticTreeDesc(static_dtree, extra_dbits, 0,          D_CODES, MAX_BITS);
  static_bl_desc = new StaticTreeDesc(new Array(0), extra_blbits, 0,         BL_CODES, MAX_BL_BITS);

  //static_init_done = true;
}


/* ===========================================================================
 * Initialize a new block.
 */
function init_block(s) {
  var n; /* iterates over tree elements */

  /* Initialize the trees. */
  for (n = 0; n < L_CODES;  n++) { s.dyn_ltree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < D_CODES;  n++) { s.dyn_dtree[n * 2]/*.Freq*/ = 0; }
  for (n = 0; n < BL_CODES; n++) { s.bl_tree[n * 2]/*.Freq*/ = 0; }

  s.dyn_ltree[END_BLOCK * 2]/*.Freq*/ = 1;
  s.opt_len = s.static_len = 0;
  s.last_lit = s.matches = 0;
}


/* ===========================================================================
 * Flush the bit buffer and align the output on a byte boundary
 */
function bi_windup(s)
{
  if (s.bi_valid > 8) {
    put_short(s, s.bi_buf);
  } else if (s.bi_valid > 0) {
    //put_byte(s, (Byte)s->bi_buf);
    s.pending_buf[s.pending++] = s.bi_buf;
  }
  s.bi_buf = 0;
  s.bi_valid = 0;
}

/* ===========================================================================
 * Copy a stored block, storing first the length and its
 * one's complement if requested.
 */
function copy_block(s, buf, len, header)
//DeflateState *s;
//charf    *buf;    /* the input data */
//unsigned len;     /* its length */
//int      header;  /* true if block header must be written */
{
  bi_windup(s);        /* align on byte boundary */

  if (header) {
    put_short(s, len);
    put_short(s, ~len);
  }
//  while (len--) {
//    put_byte(s, *buf++);
//  }
  utils.arraySet(s.pending_buf, s.window, buf, len, s.pending);
  s.pending += len;
}

/* ===========================================================================
 * Compares to subtrees, using the tree depth as tie breaker when
 * the subtrees have equal frequency. This minimizes the worst case length.
 */
function smaller(tree, n, m, depth) {
  var _n2 = n * 2;
  var _m2 = m * 2;
  return (tree[_n2]/*.Freq*/ < tree[_m2]/*.Freq*/ ||
         (tree[_n2]/*.Freq*/ === tree[_m2]/*.Freq*/ && depth[n] <= depth[m]));
}

/* ===========================================================================
 * Restore the heap property by moving down the tree starting at node k,
 * exchanging a node with the smallest of its two sons if necessary, stopping
 * when the heap property is re-established (each father smaller than its
 * two sons).
 */
function pqdownheap(s, tree, k)
//    deflate_state *s;
//    ct_data *tree;  /* the tree to restore */
//    int k;               /* node to move down */
{
  var v = s.heap[k];
  var j = k << 1;  /* left son of k */
  while (j <= s.heap_len) {
    /* Set j to the smallest of the two sons: */
    if (j < s.heap_len &&
      smaller(tree, s.heap[j + 1], s.heap[j], s.depth)) {
      j++;
    }
    /* Exit if v is smaller than both sons */
    if (smaller(tree, v, s.heap[j], s.depth)) { break; }

    /* Exchange v with the smallest son */
    s.heap[k] = s.heap[j];
    k = j;

    /* And continue down the tree, setting j to the left son of k */
    j <<= 1;
  }
  s.heap[k] = v;
}


// inlined manually
// var SMALLEST = 1;

/* ===========================================================================
 * Send the block data compressed using the given Huffman trees
 */
function compress_block(s, ltree, dtree)
//    deflate_state *s;
//    const ct_data *ltree; /* literal tree */
//    const ct_data *dtree; /* distance tree */
{
  var dist;           /* distance of matched string */
  var lc;             /* match length or unmatched char (if dist == 0) */
  var lx = 0;         /* running index in l_buf */
  var code;           /* the code to send */
  var extra;          /* number of extra bits to send */

  if (s.last_lit !== 0) {
    do {
      dist = (s.pending_buf[s.d_buf + lx * 2] << 8) | (s.pending_buf[s.d_buf + lx * 2 + 1]);
      lc = s.pending_buf[s.l_buf + lx];
      lx++;

      if (dist === 0) {
        send_code(s, lc, ltree); /* send a literal byte */
        //Tracecv(isgraph(lc), (stderr," '%c' ", lc));
      } else {
        /* Here, lc is the match length - MIN_MATCH */
        code = _length_code[lc];
        send_code(s, code + LITERALS + 1, ltree); /* send the length code */
        extra = extra_lbits[code];
        if (extra !== 0) {
          lc -= base_length[code];
          send_bits(s, lc, extra);       /* send the extra length bits */
        }
        dist--; /* dist is now the match distance - 1 */
        code = d_code(dist);
        //Assert (code < D_CODES, "bad d_code");

        send_code(s, code, dtree);       /* send the distance code */
        extra = extra_dbits[code];
        if (extra !== 0) {
          dist -= base_dist[code];
          send_bits(s, dist, extra);   /* send the extra distance bits */
        }
      } /* literal or match pair ? */

      /* Check that the overlay between pending_buf and d_buf+l_buf is ok: */
      //Assert((uInt)(s->pending) < s->lit_bufsize + 2*lx,
      //       "pendingBuf overflow");

    } while (lx < s.last_lit);
  }

  send_code(s, END_BLOCK, ltree);
}


/* ===========================================================================
 * Construct one Huffman tree and assigns the code bit strings and lengths.
 * Update the total bit length for the current block.
 * IN assertion: the field freq is set for all tree elements.
 * OUT assertions: the fields len and code are set to the optimal bit length
 *     and corresponding code. The length opt_len is updated; static_len is
 *     also updated if stree is not null. The field max_code is set.
 */
function build_tree(s, desc)
//    deflate_state *s;
//    tree_desc *desc; /* the tree descriptor */
{
  var tree     = desc.dyn_tree;
  var stree    = desc.stat_desc.static_tree;
  var has_stree = desc.stat_desc.has_stree;
  var elems    = desc.stat_desc.elems;
  var n, m;          /* iterate over heap elements */
  var max_code = -1; /* largest code with non zero frequency */
  var node;          /* new node being created */

  /* Construct the initial heap, with least frequent element in
   * heap[SMALLEST]. The sons of heap[n] are heap[2*n] and heap[2*n+1].
   * heap[0] is not used.
   */
  s.heap_len = 0;
  s.heap_max = HEAP_SIZE;

  for (n = 0; n < elems; n++) {
    if (tree[n * 2]/*.Freq*/ !== 0) {
      s.heap[++s.heap_len] = max_code = n;
      s.depth[n] = 0;

    } else {
      tree[n * 2 + 1]/*.Len*/ = 0;
    }
  }

  /* The pkzip format requires that at least one distance code exists,
   * and that at least one bit should be sent even if there is only one
   * possible code. So to avoid special checks later on we force at least
   * two codes of non zero frequency.
   */
  while (s.heap_len < 2) {
    node = s.heap[++s.heap_len] = (max_code < 2 ? ++max_code : 0);
    tree[node * 2]/*.Freq*/ = 1;
    s.depth[node] = 0;
    s.opt_len--;

    if (has_stree) {
      s.static_len -= stree[node * 2 + 1]/*.Len*/;
    }
    /* node is 0 or 1 so it does not have extra bits */
  }
  desc.max_code = max_code;

  /* The elements heap[heap_len/2+1 .. heap_len] are leaves of the tree,
   * establish sub-heaps of increasing lengths:
   */
  for (n = (s.heap_len >> 1/*int /2*/); n >= 1; n--) { pqdownheap(s, tree, n); }

  /* Construct the Huffman tree by repeatedly combining the least two
   * frequent nodes.
   */
  node = elems;              /* next internal node of the tree */
  do {
    //pqremove(s, tree, n);  /* n = node of least frequency */
    /*** pqremove ***/
    n = s.heap[1/*SMALLEST*/];
    s.heap[1/*SMALLEST*/] = s.heap[s.heap_len--];
    pqdownheap(s, tree, 1/*SMALLEST*/);
    /***/

    m = s.heap[1/*SMALLEST*/]; /* m = node of next least frequency */

    s.heap[--s.heap_max] = n; /* keep the nodes sorted by frequency */
    s.heap[--s.heap_max] = m;

    /* Create a new node father of n and m */
    tree[node * 2]/*.Freq*/ = tree[n * 2]/*.Freq*/ + tree[m * 2]/*.Freq*/;
    s.depth[node] = (s.depth[n] >= s.depth[m] ? s.depth[n] : s.depth[m]) + 1;
    tree[n * 2 + 1]/*.Dad*/ = tree[m * 2 + 1]/*.Dad*/ = node;

    /* and insert the new node in the heap */
    s.heap[1/*SMALLEST*/] = node++;
    pqdownheap(s, tree, 1/*SMALLEST*/);

  } while (s.heap_len >= 2);

  s.heap[--s.heap_max] = s.heap[1/*SMALLEST*/];

  /* At this point, the fields freq and dad are set. We can now
   * generate the bit lengths.
   */
  gen_bitlen(s, desc);

  /* The field len is now set, we can generate the bit codes */
  gen_codes(tree, max_code, s.bl_count);
}


/* ===========================================================================
 * Scan a literal or distance tree to determine the frequencies of the codes
 * in the bit length tree.
 */
function scan_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree;   /* the tree to be scanned */
//    int max_code;    /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }
  tree[(max_code + 1) * 2 + 1]/*.Len*/ = 0xffff; /* guard */

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      s.bl_tree[curlen * 2]/*.Freq*/ += count;

    } else if (curlen !== 0) {

      if (curlen !== prevlen) { s.bl_tree[curlen * 2]/*.Freq*/++; }
      s.bl_tree[REP_3_6 * 2]/*.Freq*/++;

    } else if (count <= 10) {
      s.bl_tree[REPZ_3_10 * 2]/*.Freq*/++;

    } else {
      s.bl_tree[REPZ_11_138 * 2]/*.Freq*/++;
    }

    count = 0;
    prevlen = curlen;

    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Send a literal or distance tree in compressed form, using the codes in
 * bl_tree.
 */
function send_tree(s, tree, max_code)
//    deflate_state *s;
//    ct_data *tree; /* the tree to be scanned */
//    int max_code;       /* and its largest code of non zero frequency */
{
  var n;                     /* iterates over all tree elements */
  var prevlen = -1;          /* last emitted length */
  var curlen;                /* length of current code */

  var nextlen = tree[0 * 2 + 1]/*.Len*/; /* length of next code */

  var count = 0;             /* repeat count of the current code */
  var max_count = 7;         /* max repeat count */
  var min_count = 4;         /* min repeat count */

  /* tree[max_code+1].Len = -1; */  /* guard already set */
  if (nextlen === 0) {
    max_count = 138;
    min_count = 3;
  }

  for (n = 0; n <= max_code; n++) {
    curlen = nextlen;
    nextlen = tree[(n + 1) * 2 + 1]/*.Len*/;

    if (++count < max_count && curlen === nextlen) {
      continue;

    } else if (count < min_count) {
      do { send_code(s, curlen, s.bl_tree); } while (--count !== 0);

    } else if (curlen !== 0) {
      if (curlen !== prevlen) {
        send_code(s, curlen, s.bl_tree);
        count--;
      }
      //Assert(count >= 3 && count <= 6, " 3_6?");
      send_code(s, REP_3_6, s.bl_tree);
      send_bits(s, count - 3, 2);

    } else if (count <= 10) {
      send_code(s, REPZ_3_10, s.bl_tree);
      send_bits(s, count - 3, 3);

    } else {
      send_code(s, REPZ_11_138, s.bl_tree);
      send_bits(s, count - 11, 7);
    }

    count = 0;
    prevlen = curlen;
    if (nextlen === 0) {
      max_count = 138;
      min_count = 3;

    } else if (curlen === nextlen) {
      max_count = 6;
      min_count = 3;

    } else {
      max_count = 7;
      min_count = 4;
    }
  }
}


/* ===========================================================================
 * Construct the Huffman tree for the bit lengths and return the index in
 * bl_order of the last bit length code to send.
 */
function build_bl_tree(s) {
  var max_blindex;  /* index of last bit length code of non zero freq */

  /* Determine the bit length frequencies for literal and distance trees */
  scan_tree(s, s.dyn_ltree, s.l_desc.max_code);
  scan_tree(s, s.dyn_dtree, s.d_desc.max_code);

  /* Build the bit length tree: */
  build_tree(s, s.bl_desc);
  /* opt_len now includes the length of the tree representations, except
   * the lengths of the bit lengths codes and the 5+5+4 bits for the counts.
   */

  /* Determine the number of bit length codes to send. The pkzip format
   * requires that at least 4 bit length codes be sent. (appnote.txt says
   * 3 but the actual value used is 4.)
   */
  for (max_blindex = BL_CODES - 1; max_blindex >= 3; max_blindex--) {
    if (s.bl_tree[bl_order[max_blindex] * 2 + 1]/*.Len*/ !== 0) {
      break;
    }
  }
  /* Update opt_len to include the bit length tree and counts */
  s.opt_len += 3 * (max_blindex + 1) + 5 + 5 + 4;
  //Tracev((stderr, "\ndyn trees: dyn %ld, stat %ld",
  //        s->opt_len, s->static_len));

  return max_blindex;
}


/* ===========================================================================
 * Send the header for a block using dynamic Huffman trees: the counts, the
 * lengths of the bit length codes, the literal tree and the distance tree.
 * IN assertion: lcodes >= 257, dcodes >= 1, blcodes >= 4.
 */
function send_all_trees(s, lcodes, dcodes, blcodes)
//    deflate_state *s;
//    int lcodes, dcodes, blcodes; /* number of codes for each tree */
{
  var rank;                    /* index in bl_order */

  //Assert (lcodes >= 257 && dcodes >= 1 && blcodes >= 4, "not enough codes");
  //Assert (lcodes <= L_CODES && dcodes <= D_CODES && blcodes <= BL_CODES,
  //        "too many codes");
  //Tracev((stderr, "\nbl counts: "));
  send_bits(s, lcodes - 257, 5); /* not +255 as stated in appnote.txt */
  send_bits(s, dcodes - 1,   5);
  send_bits(s, blcodes - 4,  4); /* not -3 as stated in appnote.txt */
  for (rank = 0; rank < blcodes; rank++) {
    //Tracev((stderr, "\nbl code %2d ", bl_order[rank]));
    send_bits(s, s.bl_tree[bl_order[rank] * 2 + 1]/*.Len*/, 3);
  }
  //Tracev((stderr, "\nbl tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_ltree, lcodes - 1); /* literal tree */
  //Tracev((stderr, "\nlit tree: sent %ld", s->bits_sent));

  send_tree(s, s.dyn_dtree, dcodes - 1); /* distance tree */
  //Tracev((stderr, "\ndist tree: sent %ld", s->bits_sent));
}


/* ===========================================================================
 * Check if the data type is TEXT or BINARY, using the following algorithm:
 * - TEXT if the two conditions below are satisfied:
 *    a) There are no non-portable control characters belonging to the
 *       "black list" (0..6, 14..25, 28..31).
 *    b) There is at least one printable character belonging to the
 *       "white list" (9 {TAB}, 10 {LF}, 13 {CR}, 32..255).
 * - BINARY otherwise.
 * - The following partially-portable control characters form a
 *   "gray list" that is ignored in this detection algorithm:
 *   (7 {BEL}, 8 {BS}, 11 {VT}, 12 {FF}, 26 {SUB}, 27 {ESC}).
 * IN assertion: the fields Freq of dyn_ltree are set.
 */
function detect_data_type(s) {
  /* black_mask is the bit mask of black-listed bytes
   * set bits 0..6, 14..25, and 28..31
   * 0xf3ffc07f = binary 11110011111111111100000001111111
   */
  var black_mask = 0xf3ffc07f;
  var n;

  /* Check for non-textual ("black-listed") bytes. */
  for (n = 0; n <= 31; n++, black_mask >>>= 1) {
    if ((black_mask & 1) && (s.dyn_ltree[n * 2]/*.Freq*/ !== 0)) {
      return Z_BINARY;
    }
  }

  /* Check for textual ("white-listed") bytes. */
  if (s.dyn_ltree[9 * 2]/*.Freq*/ !== 0 || s.dyn_ltree[10 * 2]/*.Freq*/ !== 0 ||
      s.dyn_ltree[13 * 2]/*.Freq*/ !== 0) {
    return Z_TEXT;
  }
  for (n = 32; n < LITERALS; n++) {
    if (s.dyn_ltree[n * 2]/*.Freq*/ !== 0) {
      return Z_TEXT;
    }
  }

  /* There are no "black-listed" or "white-listed" bytes:
   * this stream either is empty or has tolerated ("gray-listed") bytes only.
   */
  return Z_BINARY;
}


var static_init_done = false;

/* ===========================================================================
 * Initialize the tree data structures for a new zlib stream.
 */
function _tr_init(s)
{

  if (!static_init_done) {
    tr_static_init();
    static_init_done = true;
  }

  s.l_desc  = new TreeDesc(s.dyn_ltree, static_l_desc);
  s.d_desc  = new TreeDesc(s.dyn_dtree, static_d_desc);
  s.bl_desc = new TreeDesc(s.bl_tree, static_bl_desc);

  s.bi_buf = 0;
  s.bi_valid = 0;

  /* Initialize the first block of the first file: */
  init_block(s);
}


/* ===========================================================================
 * Send a stored block
 */
function _tr_stored_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  send_bits(s, (STORED_BLOCK << 1) + (last ? 1 : 0), 3);    /* send block type */
  copy_block(s, buf, stored_len, true); /* with header */
}


/* ===========================================================================
 * Send one empty static block to give enough lookahead for inflate.
 * This takes 10 bits, of which 7 may remain in the bit buffer.
 */
function _tr_align(s) {
  send_bits(s, STATIC_TREES << 1, 3);
  send_code(s, END_BLOCK, static_ltree);
  bi_flush(s);
}


/* ===========================================================================
 * Determine the best encoding for the current block: dynamic trees, static
 * trees or store, and output the encoded block to the zip file.
 */
function _tr_flush_block(s, buf, stored_len, last)
//DeflateState *s;
//charf *buf;       /* input block, or NULL if too old */
//ulg stored_len;   /* length of input block */
//int last;         /* one if this is the last block for a file */
{
  var opt_lenb, static_lenb;  /* opt_len and static_len in bytes */
  var max_blindex = 0;        /* index of last bit length code of non zero freq */

  /* Build the Huffman trees unless a stored block is forced */
  if (s.level > 0) {

    /* Check if the file is binary or text */
    if (s.strm.data_type === Z_UNKNOWN) {
      s.strm.data_type = detect_data_type(s);
    }

    /* Construct the literal and distance trees */
    build_tree(s, s.l_desc);
    // Tracev((stderr, "\nlit data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));

    build_tree(s, s.d_desc);
    // Tracev((stderr, "\ndist data: dyn %ld, stat %ld", s->opt_len,
    //        s->static_len));
    /* At this point, opt_len and static_len are the total bit lengths of
     * the compressed block data, excluding the tree representations.
     */

    /* Build the bit length tree for the above two trees, and get the index
     * in bl_order of the last bit length code to send.
     */
    max_blindex = build_bl_tree(s);

    /* Determine the best encoding. Compute the block lengths in bytes. */
    opt_lenb = (s.opt_len + 3 + 7) >>> 3;
    static_lenb = (s.static_len + 3 + 7) >>> 3;

    // Tracev((stderr, "\nopt %lu(%lu) stat %lu(%lu) stored %lu lit %u ",
    //        opt_lenb, s->opt_len, static_lenb, s->static_len, stored_len,
    //        s->last_lit));

    if (static_lenb <= opt_lenb) { opt_lenb = static_lenb; }

  } else {
    // Assert(buf != (char*)0, "lost buf");
    opt_lenb = static_lenb = stored_len + 5; /* force a stored block */
  }

  if ((stored_len + 4 <= opt_lenb) && (buf !== -1)) {
    /* 4: two words for the lengths */

    /* The test buf != NULL is only necessary if LIT_BUFSIZE > WSIZE.
     * Otherwise we can't have processed more than WSIZE input bytes since
     * the last block flush, because compression would have been
     * successful. If LIT_BUFSIZE <= WSIZE, it is never too late to
     * transform a block into a stored block.
     */
    _tr_stored_block(s, buf, stored_len, last);

  } else if (s.strategy === Z_FIXED || static_lenb === opt_lenb) {

    send_bits(s, (STATIC_TREES << 1) + (last ? 1 : 0), 3);
    compress_block(s, static_ltree, static_dtree);

  } else {
    send_bits(s, (DYN_TREES << 1) + (last ? 1 : 0), 3);
    send_all_trees(s, s.l_desc.max_code + 1, s.d_desc.max_code + 1, max_blindex + 1);
    compress_block(s, s.dyn_ltree, s.dyn_dtree);
  }
  // Assert (s->compressed_len == s->bits_sent, "bad compressed size");
  /* The above check is made mod 2^32, for files larger than 512 MB
   * and uLong implemented on 32 bits.
   */
  init_block(s);

  if (last) {
    bi_windup(s);
  }
  // Tracev((stderr,"\ncomprlen %lu(%lu) ", s->compressed_len>>3,
  //       s->compressed_len-7*last));
}

/* ===========================================================================
 * Save the match info and tally the frequency counts. Return true if
 * the current block must be flushed.
 */
function _tr_tally(s, dist, lc)
//    deflate_state *s;
//    unsigned dist;  /* distance of matched string */
//    unsigned lc;    /* match length-MIN_MATCH or unmatched char (if dist==0) */
{
  //var out_length, in_length, dcode;

  s.pending_buf[s.d_buf + s.last_lit * 2]     = (dist >>> 8) & 0xff;
  s.pending_buf[s.d_buf + s.last_lit * 2 + 1] = dist & 0xff;

  s.pending_buf[s.l_buf + s.last_lit] = lc & 0xff;
  s.last_lit++;

  if (dist === 0) {
    /* lc is the unmatched char */
    s.dyn_ltree[lc * 2]/*.Freq*/++;
  } else {
    s.matches++;
    /* Here, lc is the match length - MIN_MATCH */
    dist--;             /* dist = match distance - 1 */
    //Assert((ush)dist < (ush)MAX_DIST(s) &&
    //       (ush)lc <= (ush)(MAX_MATCH-MIN_MATCH) &&
    //       (ush)d_code(dist) < (ush)D_CODES,  "_tr_tally: bad match");

    s.dyn_ltree[(_length_code[lc] + LITERALS + 1) * 2]/*.Freq*/++;
    s.dyn_dtree[d_code(dist) * 2]/*.Freq*/++;
  }

// (!) This block is disabled in zlib defailts,
// don't enable it for binary compatibility

//#ifdef TRUNCATE_BLOCK
//  /* Try to guess if it is profitable to stop the current block here */
//  if ((s.last_lit & 0x1fff) === 0 && s.level > 2) {
//    /* Compute an upper bound for the compressed length */
//    out_length = s.last_lit*8;
//    in_length = s.strstart - s.block_start;
//
//    for (dcode = 0; dcode < D_CODES; dcode++) {
//      out_length += s.dyn_dtree[dcode*2]/*.Freq*/ * (5 + extra_dbits[dcode]);
//    }
//    out_length >>>= 3;
//    //Tracev((stderr,"\nlast_lit %u, in %ld, out ~%ld(%ld%%) ",
//    //       s->last_lit, in_length, out_length,
//    //       100L - out_length*100L/in_length));
//    if (s.matches < (s.last_lit>>1)/*int /2*/ && out_length < (in_length>>1)/*int /2*/) {
//      return true;
//    }
//  }
//#endif

  return (s.last_lit === s.lit_bufsize - 1);
  /* We avoid equality with lit_bufsize because of wraparound at 64K
   * on 16 bit machines and because stored blocks are restricted to
   * 64K-1 bytes.
   */
}

exports._tr_init  = _tr_init;
exports._tr_stored_block = _tr_stored_block;
exports._tr_flush_block  = _tr_flush_block;
exports._tr_tally = _tr_tally;
exports._tr_align = _tr_align;

},{"../utils/common":41}],53:[function(require,module,exports){
'use strict';

// (C) 1995-2013 Jean-loup Gailly and Mark Adler
// (C) 2014-2017 Vitaly Puzrin and Andrey Tupitsin
//
// This software is provided 'as-is', without any express or implied
// warranty. In no event will the authors be held liable for any damages
// arising from the use of this software.
//
// Permission is granted to anyone to use this software for any purpose,
// including commercial applications, and to alter it and redistribute it
// freely, subject to the following restrictions:
//
// 1. The origin of this software must not be misrepresented; you must not
//   claim that you wrote the original software. If you use this software
//   in a product, an acknowledgment in the product documentation would be
//   appreciated but is not required.
// 2. Altered source versions must be plainly marked as such, and must not be
//   misrepresented as being the original software.
// 3. This notice may not be removed or altered from any source distribution.

function ZStream() {
  /* next input byte */
  this.input = null; // JS specific, because we have no pointers
  this.next_in = 0;
  /* number of bytes available at input */
  this.avail_in = 0;
  /* total number of input bytes read so far */
  this.total_in = 0;
  /* next output byte should be put there */
  this.output = null; // JS specific, because we have no pointers
  this.next_out = 0;
  /* remaining free space at output */
  this.avail_out = 0;
  /* total number of bytes output so far */
  this.total_out = 0;
  /* last error message, NULL if no error */
  this.msg = ''/*Z_NULL*/;
  /* not visible by applications */
  this.state = null;
  /* best guess about the data type: binary or text */
  this.data_type = 2/*Z_UNKNOWN*/;
  /* adler32 value of the uncompressed data */
  this.adler = 0;
}

module.exports = ZStream;

},{}],54:[function(require,module,exports){
(function (global){
(function (global, undefined) {
    "use strict";

    if (global.setImmediate) {
        return;
    }

    var nextHandle = 1; // Spec says greater than zero
    var tasksByHandle = {};
    var currentlyRunningATask = false;
    var doc = global.document;
    var registerImmediate;

    function setImmediate(callback) {
      // Callback can either be a function or a string
      if (typeof callback !== "function") {
        callback = new Function("" + callback);
      }
      // Copy function arguments
      var args = new Array(arguments.length - 1);
      for (var i = 0; i < args.length; i++) {
          args[i] = arguments[i + 1];
      }
      // Store and register the task
      var task = { callback: callback, args: args };
      tasksByHandle[nextHandle] = task;
      registerImmediate(nextHandle);
      return nextHandle++;
    }

    function clearImmediate(handle) {
        delete tasksByHandle[handle];
    }

    function run(task) {
        var callback = task.callback;
        var args = task.args;
        switch (args.length) {
        case 0:
            callback();
            break;
        case 1:
            callback(args[0]);
            break;
        case 2:
            callback(args[0], args[1]);
            break;
        case 3:
            callback(args[0], args[1], args[2]);
            break;
        default:
            callback.apply(undefined, args);
            break;
        }
    }

    function runIfPresent(handle) {
        // From the spec: "Wait until any invocations of this algorithm started before this one have completed."
        // So if we're currently running a task, we'll need to delay this invocation.
        if (currentlyRunningATask) {
            // Delay by doing a setTimeout. setImmediate was tried instead, but in Firefox 7 it generated a
            // "too much recursion" error.
            setTimeout(runIfPresent, 0, handle);
        } else {
            var task = tasksByHandle[handle];
            if (task) {
                currentlyRunningATask = true;
                try {
                    run(task);
                } finally {
                    clearImmediate(handle);
                    currentlyRunningATask = false;
                }
            }
        }
    }

    function installNextTickImplementation() {
        registerImmediate = function(handle) {
            process.nextTick(function () { runIfPresent(handle); });
        };
    }

    function canUsePostMessage() {
        // The test against `importScripts` prevents this implementation from being installed inside a web worker,
        // where `global.postMessage` means something completely different and can't be used for this purpose.
        if (global.postMessage && !global.importScripts) {
            var postMessageIsAsynchronous = true;
            var oldOnMessage = global.onmessage;
            global.onmessage = function() {
                postMessageIsAsynchronous = false;
            };
            global.postMessage("", "*");
            global.onmessage = oldOnMessage;
            return postMessageIsAsynchronous;
        }
    }

    function installPostMessageImplementation() {
        // Installs an event handler on `global` for the `message` event: see
        // * https://developer.mozilla.org/en/DOM/window.postMessage
        // * http://www.whatwg.org/specs/web-apps/current-work/multipage/comms.html#crossDocumentMessages

        var messagePrefix = "setImmediate$" + Math.random() + "$";
        var onGlobalMessage = function(event) {
            if (event.source === global &&
                typeof event.data === "string" &&
                event.data.indexOf(messagePrefix) === 0) {
                runIfPresent(+event.data.slice(messagePrefix.length));
            }
        };

        if (global.addEventListener) {
            global.addEventListener("message", onGlobalMessage, false);
        } else {
            global.attachEvent("onmessage", onGlobalMessage);
        }

        registerImmediate = function(handle) {
            global.postMessage(messagePrefix + handle, "*");
        };
    }

    function installMessageChannelImplementation() {
        var channel = new MessageChannel();
        channel.port1.onmessage = function(event) {
            var handle = event.data;
            runIfPresent(handle);
        };

        registerImmediate = function(handle) {
            channel.port2.postMessage(handle);
        };
    }

    function installReadyStateChangeImplementation() {
        var html = doc.documentElement;
        registerImmediate = function(handle) {
            // Create a <script> element; its readystatechange event will be fired asynchronously once it is inserted
            // into the document. Do so, thus queuing up the task. Remember to clean up once it's been called.
            var script = doc.createElement("script");
            script.onreadystatechange = function () {
                runIfPresent(handle);
                script.onreadystatechange = null;
                html.removeChild(script);
                script = null;
            };
            html.appendChild(script);
        };
    }

    function installSetTimeoutImplementation() {
        registerImmediate = function(handle) {
            setTimeout(runIfPresent, 0, handle);
        };
    }

    // If supported, we should attach to the prototype of global, since that is where setTimeout et al. live.
    var attachTo = Object.getPrototypeOf && Object.getPrototypeOf(global);
    attachTo = attachTo && attachTo.setTimeout ? attachTo : global;

    // Don't get fooled by e.g. browserify environments.
    if ({}.toString.call(global.process) === "[object process]") {
        // For Node.js before 0.9
        installNextTickImplementation();

    } else if (canUsePostMessage()) {
        // For non-IE10 modern browsers
        installPostMessageImplementation();

    } else if (global.MessageChannel) {
        // For web workers, where supported
        installMessageChannelImplementation();

    } else if (doc && "onreadystatechange" in doc.createElement("script")) {
        // For IE 6–8
        installReadyStateChangeImplementation();

    } else {
        // For older browsers
        installSetTimeoutImplementation();
    }

    attachTo.setImmediate = setImmediate;
    attachTo.clearImmediate = clearImmediate;
}(typeof self === "undefined" ? typeof global === "undefined" ? this : global : self));

}).call(this,typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : {})
},{}]},{},[10])(10)
});
!function(a,i){"use strict";"object"==typeof module&&module.exports?module.exports=i(require("moment")):"function"==typeof define&&define.amd?define(["moment"],i):i(a.moment)}(this,function(c){"use strict";void 0===c.version&&c.default&&(c=c.default);var i,A={},n={},t={},s={},u={};c&&"string"==typeof c.version||O("Moment Timezone requires Moment.js. See https://momentjs.com/timezone/docs/#/use-it/browser/");var a=c.version.split("."),e=+a[0],r=+a[1];function m(a){return 96<a?a-87:64<a?a-29:a-48}function o(a){var i=0,e=a.split("."),r=e[0],o=e[1]||"",c=1,A=0,n=1;for(45===a.charCodeAt(0)&&(n=-(i=1));i<r.length;i++)A=60*A+m(r.charCodeAt(i));for(i=0;i<o.length;i++)c/=60,A+=m(o.charCodeAt(i))*c;return A*n}function f(a){for(var i=0;i<a.length;i++)a[i]=o(a[i])}function l(a,i){var e,r=[];for(e=0;e<i.length;e++)r[e]=a[i[e]];return r}function p(a){var i=a.split("|"),e=i[2].split(" "),r=i[3].split(""),o=i[4].split(" ");return f(e),f(r),f(o),function(a,i){for(var e=0;e<i;e++)a[e]=Math.round((a[e-1]||0)+6e4*a[e]);a[i-1]=1/0}(o,r.length),{name:i[0],abbrs:l(i[1].split(" "),r),offsets:l(e,r),untils:o,population:0|i[5]}}function M(a){a&&this._set(p(a))}function d(a,i){this.name=a,this.zones=i}function b(a){var i=a.toTimeString(),e=i.match(/\([a-z ]+\)/i);"GMT"===(e=e&&e[0]?(e=e[0].match(/[A-Z]/g))?e.join(""):void 0:(e=i.match(/[A-Z]{3,5}/g))?e[0]:void 0)&&(e=void 0),this.at=+a,this.abbr=e,this.offset=a.getTimezoneOffset()}function h(a){this.zone=a,this.offsetScore=0,this.abbrScore=0}function g(a,i){for(var e,r;r=6e4*((i.at-a.at)/12e4|0);)(e=new b(new Date(a.at+r))).offset===a.offset?a=e:i=e;return a}function E(a,i){return a.offsetScore!==i.offsetScore?a.offsetScore-i.offsetScore:a.abbrScore!==i.abbrScore?a.abbrScore-i.abbrScore:a.zone.population!==i.zone.population?i.zone.population-a.zone.population:i.zone.name.localeCompare(a.zone.name)}function z(a,i){var e,r;for(f(i),e=0;e<i.length;e++)r=i[e],u[r]=u[r]||{},u[r][a]=!0}function P(){try{var a=Intl.DateTimeFormat().resolvedOptions().timeZone;if(a&&3<a.length){var i=s[S(a)];if(i)return i;O("Moment Timezone found "+a+" from the Intl api, but did not have that data loaded.")}}catch(a){}var e,r,o,c=function(){var a,i,e,r=(new Date).getFullYear()-2,o=new b(new Date(r,0,1)),c=[o];for(e=1;e<48;e++)(i=new b(new Date(r,e,1))).offset!==o.offset&&(a=g(o,i),c.push(a),c.push(new b(new Date(a.at+6e4)))),o=i;for(e=0;e<4;e++)c.push(new b(new Date(r+e,0,1))),c.push(new b(new Date(r+e,6,1)));return c}(),A=c.length,n=function(a){var i,e,r,o=a.length,c={},A=[];for(i=0;i<o;i++)for(e in r=u[a[i].offset]||{})r.hasOwnProperty(e)&&(c[e]=!0);for(i in c)c.hasOwnProperty(i)&&A.push(s[i]);return A}(c),t=[];for(r=0;r<n.length;r++){for(e=new h(_(n[r]),A),o=0;o<A;o++)e.scoreOffsetAt(c[o]);t.push(e)}return t.sort(E),0<t.length?t[0].zone.name:void 0}function S(a){return(a||"").toLowerCase().replace(/\//g,"_")}function T(a){var i,e,r,o;for("string"==typeof a&&(a=[a]),i=0;i<a.length;i++)o=S(e=(r=a[i].split("|"))[0]),A[o]=a[i],s[o]=e,z(o,r[2].split(" "))}function _(a,i){a=S(a);var e,r=A[a];return r instanceof M?r:"string"==typeof r?(r=new M(r),A[a]=r):n[a]&&i!==_&&(e=_(n[a],_))?((r=A[a]=new M)._set(e),r.name=s[a],r):null}function k(a){var i,e,r,o;for("string"==typeof a&&(a=[a]),i=0;i<a.length;i++)r=S((e=a[i].split("|"))[0]),o=S(e[1]),n[r]=o,s[r]=e[0],n[o]=r,s[o]=e[1]}function B(a){T(a.zones),k(a.links),function(a){var i,e,r,o;if(a&&a.length)for(i=0;i<a.length;i++)e=(o=a[i].split("|"))[0].toUpperCase(),r=o[1].split(" "),t[e]=new d(e,r)}(a.countries),L.dataVersion=a.version}function C(a){var i="X"===a._f||"x"===a._f;return!(!a._a||void 0!==a._tzm||i)}function O(a){"undefined"!=typeof console&&"function"==typeof console.error&&console.error(a)}function L(a){var i=Array.prototype.slice.call(arguments,0,-1),e=arguments[arguments.length-1],r=_(e),o=c.utc.apply(null,i);return r&&!c.isMoment(a)&&C(o)&&o.add(r.parse(o),"minutes"),o.tz(e),o}(e<2||2==e&&r<6)&&O("Moment Timezone requires Moment.js >= 2.6.0. You are using Moment.js "+c.version+". See momentjs.com"),M.prototype={_set:function(a){this.name=a.name,this.abbrs=a.abbrs,this.untils=a.untils,this.offsets=a.offsets,this.population=a.population},_index:function(a){var i,e=+a,r=this.untils;for(i=0;i<r.length;i++)if(e<r[i])return i},countries:function(){var i=this.name;return Object.keys(t).filter(function(a){return-1!==t[a].zones.indexOf(i)})},parse:function(a){var i,e,r,o,c=+a,A=this.offsets,n=this.untils,t=n.length-1;for(o=0;o<t;o++)if(i=A[o],e=A[o+1],r=A[o?o-1:o],i<e&&L.moveAmbiguousForward?i=e:r<i&&L.moveInvalidForward&&(i=r),c<n[o]-6e4*i)return A[o];return A[t]},abbr:function(a){return this.abbrs[this._index(a)]},offset:function(a){return O("zone.offset has been deprecated in favor of zone.utcOffset"),this.offsets[this._index(a)]},utcOffset:function(a){return this.offsets[this._index(a)]}},h.prototype.scoreOffsetAt=function(a){this.offsetScore+=Math.abs(this.zone.utcOffset(a.at)-a.offset),this.zone.abbr(a.at).replace(/[^A-Z]/g,"")!==a.abbr&&this.abbrScore++},L.version="0.5.31",L.dataVersion="",L._zones=A,L._links=n,L._names=s,L._countries=t,L.add=T,L.link=k,L.load=B,L.zone=_,L.zoneExists=function a(i){return a.didShowError||(a.didShowError=!0,O("moment.tz.zoneExists('"+i+"') has been deprecated in favor of !moment.tz.zone('"+i+"')")),!!_(i)},L.guess=function(a){return i&&!a||(i=P()),i},L.names=function(){var a,i=[];for(a in s)s.hasOwnProperty(a)&&(A[a]||A[n[a]])&&s[a]&&i.push(s[a]);return i.sort()},L.Zone=M,L.unpack=p,L.unpackBase60=o,L.needsOffset=C,L.moveInvalidForward=!0,L.moveAmbiguousForward=!1,L.countries=function(){return Object.keys(t)},L.zonesForCountry=function(a,i){if(!(a=function(a){return a=a.toUpperCase(),t[a]||null}(a)))return null;var e=a.zones.sort();return i?e.map(function(a){return{name:a,offset:_(a).utcOffset(new Date)}}):e};var y,N=c.fn;function D(a){return function(){return this._z?this._z.abbr(this):a.call(this)}}function v(a){return function(){return this._z=null,a.apply(this,arguments)}}c.tz=L,c.defaultZone=null,c.updateOffset=function(a,i){var e,r=c.defaultZone;if(void 0===a._z&&(r&&C(a)&&!a._isUTC&&(a._d=c.utc(a._a)._d,a.utc().add(r.parse(a),"minutes")),a._z=r),a._z)if(e=a._z.utcOffset(a),Math.abs(e)<16&&(e/=60),void 0!==a.utcOffset){var o=a._z;a.utcOffset(-e,i),a._z=o}else a.zone(e,i)},N.tz=function(a,i){if(a){if("string"!=typeof a)throw new Error("Time zone name must be a string, got "+a+" ["+typeof a+"]");return this._z=_(a),this._z?c.updateOffset(this,i):O("Moment Timezone has no data for "+a+". See http://momentjs.com/timezone/docs/#/data-loading/."),this}if(this._z)return this._z.name},N.zoneName=D(N.zoneName),N.zoneAbbr=D(N.zoneAbbr),N.utc=v(N.utc),N.local=v(N.local),N.utcOffset=(y=N.utcOffset,function(){return 0<arguments.length&&(this._z=null),y.apply(this,arguments)}),c.tz.setDefault=function(a){return(e<2||2==e&&r<9)&&O("Moment Timezone setDefault() requires Moment.js >= 2.9.0. You are using Moment.js "+c.version+"."),c.defaultZone=a?_(a):null,c};var G=c.momentProperties;return"[object Array]"===Object.prototype.toString.call(G)?(G.push("_z"),G.push("_a")):G&&(G._z=null),B({version:"2020a",zones:["Africa/Abidjan|GMT|0|0||48e5","Africa/Nairobi|EAT|-30|0||47e5","Africa/Algiers|CET|-10|0||26e5","Africa/Lagos|WAT|-10|0||17e6","Africa/Maputo|CAT|-20|0||26e5","Africa/Cairo|EET|-20|0||15e6","Africa/Casablanca|+00 +01|0 -10|010101010101010101010101010101|1O9e0 uM0 e00 Dc0 11A0 s00 e00 IM0 WM0 mo0 gM0 LA0 WM0 jA0 e00 28M0 e00 2600 gM0 2600 e00 2600 gM0 2600 e00 28M0 e00 2600 gM0|32e5","Europe/Paris|CET CEST|-10 -20|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|11e6","Africa/Johannesburg|SAST|-20|0||84e5","Africa/Khartoum|EAT CAT|-30 -20|01|1Usl0|51e5","Africa/Sao_Tome|GMT WAT|0 -10|010|1UQN0 2q00|","Africa/Windhoek|CAT WAT|-20 -10|0101010|1Oc00 11B0 1nX0 11B0 1nX0 11B0|32e4","America/Adak|HST HDT|a0 90|01010101010101010101010|1O100 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|326","America/Anchorage|AKST AKDT|90 80|01010101010101010101010|1O0X0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|30e4","America/Santo_Domingo|AST|40|0||29e5","America/Fortaleza|-03|30|0||34e5","America/Asuncion|-03 -04|30 40|01010101010101010101010|1O6r0 1ip0 19X0 1fB0 19X0 1fB0 19X0 1ip0 17b0 1ip0 17b0 1ip0 19X0 1fB0 19X0 1fB0 19X0 1fB0 19X0 1ip0 17b0 1ip0|28e5","America/Panama|EST|50|0||15e5","America/Mexico_City|CST CDT|60 50|01010101010101010101010|1Oc80 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0|20e6","America/Managua|CST|60|0||22e5","America/La_Paz|-04|40|0||19e5","America/Lima|-05|50|0||11e6","America/Denver|MST MDT|70 60|01010101010101010101010|1O0V0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|26e5","America/Campo_Grande|-03 -04|30 40|0101010101|1NTf0 1zd0 On0 1zd0 On0 1zd0 On0 1HB0 FX0|77e4","America/Cancun|CST EST|60 50|01|1NKU0|63e4","America/Caracas|-0430 -04|4u 40|01|1QMT0|29e5","America/Chicago|CST CDT|60 50|01010101010101010101010|1O0U0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|92e5","America/Chihuahua|MST MDT|70 60|01010101010101010101010|1Oc90 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0 14p0 1nX0 11B0 1nX0 11B0 1nX0 14p0 1lb0 14p0 1lb0|81e4","America/Phoenix|MST|70|0||42e5","America/Whitehorse|PST PDT MST|80 70 70|010101010102|1O0W0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0|23e3","America/New_York|EST EDT|50 40|01010101010101010101010|1O0T0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|21e6","America/Los_Angeles|PST PDT|80 70|01010101010101010101010|1O0W0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|15e6","America/Fort_Nelson|PST MST|80 70|01|1O0W0|39e2","America/Halifax|AST ADT|40 30|01010101010101010101010|1O0S0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|39e4","America/Godthab|-03 -02|30 20|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|17e3","America/Grand_Turk|EST EDT AST|50 40 40|0121010101010101010|1O0T0 1zb0 5Ip0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|37e2","America/Havana|CST CDT|50 40|01010101010101010101010|1O0R0 1zc0 Rc0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0 Rc0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0 Oo0 1zc0|21e5","America/Metlakatla|PST AKST AKDT|80 90 80|01212120121212121212121|1PAa0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 uM0 jB0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|14e2","America/Miquelon|-03 -02|30 20|01010101010101010101010|1O0R0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|61e2","America/Montevideo|-02 -03|20 30|01|1O0Q0|17e5","America/Noronha|-02|20|0||30e2","America/Port-au-Prince|EST EDT|50 40|010101010101010101010|1O0T0 1zb0 3iN0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|23e5","Antarctica/Palmer|-03 -04|30 40|010|1QSr0 Ap0|40","America/Santiago|-03 -04|30 40|010101010101010101010|1QSr0 Ap0 1Nb0 Ap0 1Nb0 Ap0 1zb0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 11B0 1nX0 11B0|62e5","America/Sao_Paulo|-02 -03|20 30|0101010101|1NTe0 1zd0 On0 1zd0 On0 1zd0 On0 1HB0 FX0|20e6","Atlantic/Azores|-01 +00|10 0|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|25e4","America/St_Johns|NST NDT|3u 2u|01010101010101010101010|1O0Ru 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Rd0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0 Op0 1zb0|11e4","Antarctica/Casey|+08 +11|-80 -b0|010|1RWg0 3m10|10","Asia/Bangkok|+07|-70|0||15e6","Asia/Vladivostok|+10|-a0|0||60e4","Pacific/Bougainville|+11|-b0|0||18e4","Asia/Tashkent|+05|-50|0||23e5","Pacific/Auckland|NZDT NZST|-d0 -c0|01010101010101010101010|1ObO0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1io0 1a00 1fA0 1a00|14e5","Asia/Baghdad|+03|-30|0||66e5","Antarctica/Troll|+00 +02|0 -20|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|40","Asia/Dhaka|+06|-60|0||16e6","Asia/Amman|EET EEST|-20 -30|01010101010101010101010|1O8m0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1o00 11A0 1qM0|25e5","Asia/Kamchatka|+12|-c0|0||18e4","Asia/Baku|+04 +05|-40 -50|010|1O9c0 1o00|27e5","Asia/Barnaul|+06 +07|-60 -70|01|1QyI0|","Asia/Beirut|EET EEST|-20 -30|01010101010101010101010|1O9a0 1nX0 11B0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0|22e5","Asia/Kuala_Lumpur|+08|-80|0||71e5","Asia/Kolkata|IST|-5u|0||15e6","Asia/Chita|+08 +09|-80 -90|01|1QyG0|33e4","Asia/Ulaanbaatar|+08 +09|-80 -90|01010|1O8G0 1cJ0 1cP0 1cJ0|12e5","Asia/Shanghai|CST|-80|0||23e6","Asia/Colombo|+0530|-5u|0||22e5","Asia/Damascus|EET EEST|-20 -30|01010101010101010101010|1O8m0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 WN0 1qL0 WN0 1qL0 11B0 1nX0 11B0 1nX0 11B0 1qL0|26e5","Asia/Yakutsk|+09|-90|0||28e4","Asia/Dubai|+04|-40|0||39e5","Asia/Famagusta|EET EEST +03|-20 -30 -30|0101201010101010101010|1O9d0 1o00 11A0 15U0 2Ks0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|","Asia/Gaza|EET EEST|-20 -30|01010101010101010101010|1O8K0 1nz0 1220 1qL0 WN0 1qL0 WN0 1qL0 11c0 1oo0 11c0 1rc0 Wo0 1rc0 Wo0 1rc0 11c0 1oo0 11c0 1oo0 11c0 1oo0|18e5","Asia/Hong_Kong|HKT|-80|0||73e5","Asia/Hovd|+07 +08|-70 -80|01010|1O8H0 1cJ0 1cP0 1cJ0|81e3","Europe/Istanbul|EET EEST +03|-20 -30 -30|01012|1O9d0 1tA0 U00 15w0|13e6","Asia/Jakarta|WIB|-70|0||31e6","Asia/Jayapura|WIT|-90|0||26e4","Asia/Jerusalem|IST IDT|-20 -30|01010101010101010101010|1O8o0 1oL0 10N0 1rz0 W10 1rz0 W10 1rz0 10N0 1oL0 10N0 1oL0 10N0 1rz0 W10 1rz0 W10 1rz0 10N0 1oL0 10N0 1oL0|81e4","Asia/Kabul|+0430|-4u|0||46e5","Asia/Karachi|PKT|-50|0||24e6","Asia/Kathmandu|+0545|-5J|0||12e5","Asia/Magadan|+10 +11|-a0 -b0|01|1QJQ0|95e3","Asia/Makassar|WITA|-80|0||15e5","Asia/Manila|PST|-80|0||24e6","Europe/Athens|EET EEST|-20 -30|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|35e5","Asia/Novosibirsk|+06 +07|-60 -70|01|1Rmk0|15e5","Asia/Pyongyang|KST KST|-90 -8u|010|1P4D0 6BA0|29e5","Asia/Qyzylorda|+06 +05|-60 -50|01|1Xei0|73e4","Asia/Rangoon|+0630|-6u|0||48e5","Asia/Sakhalin|+10 +11|-a0 -b0|01|1QyE0|58e4","Asia/Seoul|KST|-90|0||23e6","Asia/Tehran|+0330 +0430|-3u -4u|01010101010101010101010|1O6ku 1dz0 1cp0 1dz0 1cN0 1dz0 1cp0 1dz0 1cp0 1dz0 1cp0 1dz0 1cN0 1dz0 1cp0 1dz0 1cp0 1dz0 1cp0 1dz0 1cN0 1dz0|14e6","Asia/Tokyo|JST|-90|0||38e6","Asia/Tomsk|+06 +07|-60 -70|01|1QXU0|10e5","Europe/Lisbon|WET WEST|0 -10|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|27e5","Atlantic/Cape_Verde|-01|10|0||50e4","Australia/Sydney|AEDT AEST|-b0 -a0|01010101010101010101010|1ObQ0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0|40e5","Australia/Adelaide|ACDT ACST|-au -9u|01010101010101010101010|1ObQu 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0|11e5","Australia/Brisbane|AEST|-a0|0||20e5","Australia/Darwin|ACST|-9u|0||12e4","Australia/Eucla|+0845|-8J|0||368","Australia/Lord_Howe|+11 +1030|-b0 -au|01010101010101010101010|1ObP0 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1fAu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1cLu 1cMu 1fzu 1cMu 1cLu 1cMu|347","Australia/Perth|AWST|-80|0||18e5","Pacific/Easter|-05 -06|50 60|010101010101010101010|1QSr0 Ap0 1Nb0 Ap0 1Nb0 Ap0 1zb0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1nX0 11B0 1qL0 11B0 1nX0 11B0|30e2","Europe/Dublin|GMT IST|0 -10|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|12e5","Etc/GMT-1|+01|-10|0||","Pacific/Fakaofo|+13|-d0|0||483","Pacific/Kiritimati|+14|-e0|0||51e2","Etc/GMT-2|+02|-20|0||","Pacific/Tahiti|-10|a0|0||18e4","Pacific/Niue|-11|b0|0||12e2","Etc/GMT+12|-12|c0|0||","Pacific/Galapagos|-06|60|0||25e3","Etc/GMT+7|-07|70|0||","Pacific/Pitcairn|-08|80|0||56","Pacific/Gambier|-09|90|0||125","Etc/UTC|UTC|0|0||","Europe/Ulyanovsk|+03 +04|-30 -40|01|1QyL0|13e5","Europe/London|GMT BST|0 -10|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|10e6","Europe/Chisinau|EET EEST|-20 -30|01010101010101010101010|1O9c0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|67e4","Europe/Moscow|MSK|-30|0||16e6","Europe/Saratov|+03 +04|-30 -40|01|1Sfz0|","Europe/Volgograd|+03 +04|-30 -40|01|1WQL0|10e5","Pacific/Honolulu|HST|a0|0||37e4","MET|MET MEST|-10 -20|01010101010101010101010|1O9d0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00 11A0 1qM0 WM0 1qM0 WM0 1qM0 11A0 1o00 11A0 1o00|","Pacific/Chatham|+1345 +1245|-dJ -cJ|01010101010101010101010|1ObO0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1io0 1a00 1fA0 1a00|600","Pacific/Apia|+14 +13|-e0 -d0|01010101010101010101010|1ObO0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1cM0 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1fA0 1a00 1io0 1a00 1fA0 1a00|37e3","Pacific/Fiji|+13 +12|-d0 -c0|01010101010101010101010|1NF20 1SM0 uM0 1VA0 s00 1VA0 s00 1VA0 s00 20o0 pc0 20o0 s00 20o0 pc0 20o0 pc0 20o0 pc0 20o0 pc0 20o0|88e4","Pacific/Guam|ChST|-a0|0||17e4","Pacific/Marquesas|-0930|9u|0||86e2","Pacific/Pago_Pago|SST|b0|0||37e2","Pacific/Norfolk|+1130 +11 +12|-bu -b0 -c0|012121212121212|1PoCu 9Jcu 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1cM0 1fA0 1cM0 1cM0 1cM0|25e4","Pacific/Tongatapu|+13 +14|-d0 -e0|010|1S4d0 s00|75e3"],links:["Africa/Abidjan|Africa/Accra","Africa/Abidjan|Africa/Bamako","Africa/Abidjan|Africa/Banjul","Africa/Abidjan|Africa/Bissau","Africa/Abidjan|Africa/Conakry","Africa/Abidjan|Africa/Dakar","Africa/Abidjan|Africa/Freetown","Africa/Abidjan|Africa/Lome","Africa/Abidjan|Africa/Monrovia","Africa/Abidjan|Africa/Nouakchott","Africa/Abidjan|Africa/Ouagadougou","Africa/Abidjan|Africa/Timbuktu","Africa/Abidjan|America/Danmarkshavn","Africa/Abidjan|Atlantic/Reykjavik","Africa/Abidjan|Atlantic/St_Helena","Africa/Abidjan|Etc/GMT","Africa/Abidjan|Etc/GMT+0","Africa/Abidjan|Etc/GMT-0","Africa/Abidjan|Etc/GMT0","Africa/Abidjan|Etc/Greenwich","Africa/Abidjan|GMT","Africa/Abidjan|GMT+0","Africa/Abidjan|GMT-0","Africa/Abidjan|GMT0","Africa/Abidjan|Greenwich","Africa/Abidjan|Iceland","Africa/Algiers|Africa/Tunis","Africa/Cairo|Africa/Tripoli","Africa/Cairo|Egypt","Africa/Cairo|Europe/Kaliningrad","Africa/Cairo|Libya","Africa/Casablanca|Africa/El_Aaiun","Africa/Johannesburg|Africa/Maseru","Africa/Johannesburg|Africa/Mbabane","Africa/Lagos|Africa/Bangui","Africa/Lagos|Africa/Brazzaville","Africa/Lagos|Africa/Douala","Africa/Lagos|Africa/Kinshasa","Africa/Lagos|Africa/Libreville","Africa/Lagos|Africa/Luanda","Africa/Lagos|Africa/Malabo","Africa/Lagos|Africa/Ndjamena","Africa/Lagos|Africa/Niamey","Africa/Lagos|Africa/Porto-Novo","Africa/Maputo|Africa/Blantyre","Africa/Maputo|Africa/Bujumbura","Africa/Maputo|Africa/Gaborone","Africa/Maputo|Africa/Harare","Africa/Maputo|Africa/Kigali","Africa/Maputo|Africa/Lubumbashi","Africa/Maputo|Africa/Lusaka","Africa/Nairobi|Africa/Addis_Ababa","Africa/Nairobi|Africa/Asmara","Africa/Nairobi|Africa/Asmera","Africa/Nairobi|Africa/Dar_es_Salaam","Africa/Nairobi|Africa/Djibouti","Africa/Nairobi|Africa/Juba","Africa/Nairobi|Africa/Kampala","Africa/Nairobi|Africa/Mogadishu","Africa/Nairobi|Indian/Antananarivo","Africa/Nairobi|Indian/Comoro","Africa/Nairobi|Indian/Mayotte","America/Adak|America/Atka","America/Adak|US/Aleutian","America/Anchorage|America/Juneau","America/Anchorage|America/Nome","America/Anchorage|America/Sitka","America/Anchorage|America/Yakutat","America/Anchorage|US/Alaska","America/Campo_Grande|America/Cuiaba","America/Chicago|America/Indiana/Knox","America/Chicago|America/Indiana/Tell_City","America/Chicago|America/Knox_IN","America/Chicago|America/Matamoros","America/Chicago|America/Menominee","America/Chicago|America/North_Dakota/Beulah","America/Chicago|America/North_Dakota/Center","America/Chicago|America/North_Dakota/New_Salem","America/Chicago|America/Rainy_River","America/Chicago|America/Rankin_Inlet","America/Chicago|America/Resolute","America/Chicago|America/Winnipeg","America/Chicago|CST6CDT","America/Chicago|Canada/Central","America/Chicago|US/Central","America/Chicago|US/Indiana-Starke","America/Chihuahua|America/Mazatlan","America/Chihuahua|Mexico/BajaSur","America/Denver|America/Boise","America/Denver|America/Cambridge_Bay","America/Denver|America/Edmonton","America/Denver|America/Inuvik","America/Denver|America/Ojinaga","America/Denver|America/Shiprock","America/Denver|America/Yellowknife","America/Denver|Canada/Mountain","America/Denver|MST7MDT","America/Denver|Navajo","America/Denver|US/Mountain","America/Fortaleza|America/Araguaina","America/Fortaleza|America/Argentina/Buenos_Aires","America/Fortaleza|America/Argentina/Catamarca","America/Fortaleza|America/Argentina/ComodRivadavia","America/Fortaleza|America/Argentina/Cordoba","America/Fortaleza|America/Argentina/Jujuy","America/Fortaleza|America/Argentina/La_Rioja","America/Fortaleza|America/Argentina/Mendoza","America/Fortaleza|America/Argentina/Rio_Gallegos","America/Fortaleza|America/Argentina/Salta","America/Fortaleza|America/Argentina/San_Juan","America/Fortaleza|America/Argentina/San_Luis","America/Fortaleza|America/Argentina/Tucuman","America/Fortaleza|America/Argentina/Ushuaia","America/Fortaleza|America/Bahia","America/Fortaleza|America/Belem","America/Fortaleza|America/Buenos_Aires","America/Fortaleza|America/Catamarca","America/Fortaleza|America/Cayenne","America/Fortaleza|America/Cordoba","America/Fortaleza|America/Jujuy","America/Fortaleza|America/Maceio","America/Fortaleza|America/Mendoza","America/Fortaleza|America/Paramaribo","America/Fortaleza|America/Recife","America/Fortaleza|America/Rosario","America/Fortaleza|America/Santarem","America/Fortaleza|Antarctica/Rothera","America/Fortaleza|Atlantic/Stanley","America/Fortaleza|Etc/GMT+3","America/Godthab|America/Nuuk","America/Halifax|America/Glace_Bay","America/Halifax|America/Goose_Bay","America/Halifax|America/Moncton","America/Halifax|America/Thule","America/Halifax|Atlantic/Bermuda","America/Halifax|Canada/Atlantic","America/Havana|Cuba","America/La_Paz|America/Boa_Vista","America/La_Paz|America/Guyana","America/La_Paz|America/Manaus","America/La_Paz|America/Porto_Velho","America/La_Paz|Brazil/West","America/La_Paz|Etc/GMT+4","America/Lima|America/Bogota","America/Lima|America/Eirunepe","America/Lima|America/Guayaquil","America/Lima|America/Porto_Acre","America/Lima|America/Rio_Branco","America/Lima|Brazil/Acre","America/Lima|Etc/GMT+5","America/Los_Angeles|America/Ensenada","America/Los_Angeles|America/Santa_Isabel","America/Los_Angeles|America/Tijuana","America/Los_Angeles|America/Vancouver","America/Los_Angeles|Canada/Pacific","America/Los_Angeles|Mexico/BajaNorte","America/Los_Angeles|PST8PDT","America/Los_Angeles|US/Pacific","America/Los_Angeles|US/Pacific-New","America/Managua|America/Belize","America/Managua|America/Costa_Rica","America/Managua|America/El_Salvador","America/Managua|America/Guatemala","America/Managua|America/Regina","America/Managua|America/Swift_Current","America/Managua|America/Tegucigalpa","America/Managua|Canada/Saskatchewan","America/Mexico_City|America/Bahia_Banderas","America/Mexico_City|America/Merida","America/Mexico_City|America/Monterrey","America/Mexico_City|Mexico/General","America/New_York|America/Detroit","America/New_York|America/Fort_Wayne","America/New_York|America/Indiana/Indianapolis","America/New_York|America/Indiana/Marengo","America/New_York|America/Indiana/Petersburg","America/New_York|America/Indiana/Vevay","America/New_York|America/Indiana/Vincennes","America/New_York|America/Indiana/Winamac","America/New_York|America/Indianapolis","America/New_York|America/Iqaluit","America/New_York|America/Kentucky/Louisville","America/New_York|America/Kentucky/Monticello","America/New_York|America/Louisville","America/New_York|America/Montreal","America/New_York|America/Nassau","America/New_York|America/Nipigon","America/New_York|America/Pangnirtung","America/New_York|America/Thunder_Bay","America/New_York|America/Toronto","America/New_York|Canada/Eastern","America/New_York|EST5EDT","America/New_York|US/East-Indiana","America/New_York|US/Eastern","America/New_York|US/Michigan","America/Noronha|Atlantic/South_Georgia","America/Noronha|Brazil/DeNoronha","America/Noronha|Etc/GMT+2","America/Panama|America/Atikokan","America/Panama|America/Cayman","America/Panama|America/Coral_Harbour","America/Panama|America/Jamaica","America/Panama|EST","America/Panama|Jamaica","America/Phoenix|America/Creston","America/Phoenix|America/Dawson_Creek","America/Phoenix|America/Hermosillo","America/Phoenix|MST","America/Phoenix|US/Arizona","America/Santiago|Chile/Continental","America/Santo_Domingo|America/Anguilla","America/Santo_Domingo|America/Antigua","America/Santo_Domingo|America/Aruba","America/Santo_Domingo|America/Barbados","America/Santo_Domingo|America/Blanc-Sablon","America/Santo_Domingo|America/Curacao","America/Santo_Domingo|America/Dominica","America/Santo_Domingo|America/Grenada","America/Santo_Domingo|America/Guadeloupe","America/Santo_Domingo|America/Kralendijk","America/Santo_Domingo|America/Lower_Princes","America/Santo_Domingo|America/Marigot","America/Santo_Domingo|America/Martinique","America/Santo_Domingo|America/Montserrat","America/Santo_Domingo|America/Port_of_Spain","America/Santo_Domingo|America/Puerto_Rico","America/Santo_Domingo|America/St_Barthelemy","America/Santo_Domingo|America/St_Kitts","America/Santo_Domingo|America/St_Lucia","America/Santo_Domingo|America/St_Thomas","America/Santo_Domingo|America/St_Vincent","America/Santo_Domingo|America/Tortola","America/Santo_Domingo|America/Virgin","America/Sao_Paulo|Brazil/East","America/St_Johns|Canada/Newfoundland","America/Whitehorse|America/Dawson","America/Whitehorse|Canada/Yukon","Antarctica/Palmer|America/Punta_Arenas","Asia/Baghdad|Antarctica/Syowa","Asia/Baghdad|Asia/Aden","Asia/Baghdad|Asia/Bahrain","Asia/Baghdad|Asia/Kuwait","Asia/Baghdad|Asia/Qatar","Asia/Baghdad|Asia/Riyadh","Asia/Baghdad|Etc/GMT-3","Asia/Baghdad|Europe/Kirov","Asia/Baghdad|Europe/Minsk","Asia/Bangkok|Antarctica/Davis","Asia/Bangkok|Asia/Ho_Chi_Minh","Asia/Bangkok|Asia/Krasnoyarsk","Asia/Bangkok|Asia/Novokuznetsk","Asia/Bangkok|Asia/Phnom_Penh","Asia/Bangkok|Asia/Saigon","Asia/Bangkok|Asia/Vientiane","Asia/Bangkok|Etc/GMT-7","Asia/Bangkok|Indian/Christmas","Asia/Dhaka|Antarctica/Vostok","Asia/Dhaka|Asia/Almaty","Asia/Dhaka|Asia/Bishkek","Asia/Dhaka|Asia/Dacca","Asia/Dhaka|Asia/Kashgar","Asia/Dhaka|Asia/Omsk","Asia/Dhaka|Asia/Qostanay","Asia/Dhaka|Asia/Thimbu","Asia/Dhaka|Asia/Thimphu","Asia/Dhaka|Asia/Urumqi","Asia/Dhaka|Etc/GMT-6","Asia/Dhaka|Indian/Chagos","Asia/Dubai|Asia/Muscat","Asia/Dubai|Asia/Tbilisi","Asia/Dubai|Asia/Yerevan","Asia/Dubai|Etc/GMT-4","Asia/Dubai|Europe/Samara","Asia/Dubai|Indian/Mahe","Asia/Dubai|Indian/Mauritius","Asia/Dubai|Indian/Reunion","Asia/Gaza|Asia/Hebron","Asia/Hong_Kong|Hongkong","Asia/Jakarta|Asia/Pontianak","Asia/Jerusalem|Asia/Tel_Aviv","Asia/Jerusalem|Israel","Asia/Kamchatka|Asia/Anadyr","Asia/Kamchatka|Etc/GMT-12","Asia/Kamchatka|Kwajalein","Asia/Kamchatka|Pacific/Funafuti","Asia/Kamchatka|Pacific/Kwajalein","Asia/Kamchatka|Pacific/Majuro","Asia/Kamchatka|Pacific/Nauru","Asia/Kamchatka|Pacific/Tarawa","Asia/Kamchatka|Pacific/Wake","Asia/Kamchatka|Pacific/Wallis","Asia/Kathmandu|Asia/Katmandu","Asia/Kolkata|Asia/Calcutta","Asia/Kuala_Lumpur|Asia/Brunei","Asia/Kuala_Lumpur|Asia/Irkutsk","Asia/Kuala_Lumpur|Asia/Kuching","Asia/Kuala_Lumpur|Asia/Singapore","Asia/Kuala_Lumpur|Etc/GMT-8","Asia/Kuala_Lumpur|Singapore","Asia/Makassar|Asia/Ujung_Pandang","Asia/Rangoon|Asia/Yangon","Asia/Rangoon|Indian/Cocos","Asia/Seoul|ROK","Asia/Shanghai|Asia/Chongqing","Asia/Shanghai|Asia/Chungking","Asia/Shanghai|Asia/Harbin","Asia/Shanghai|Asia/Macao","Asia/Shanghai|Asia/Macau","Asia/Shanghai|Asia/Taipei","Asia/Shanghai|PRC","Asia/Shanghai|ROC","Asia/Tashkent|Antarctica/Mawson","Asia/Tashkent|Asia/Aqtau","Asia/Tashkent|Asia/Aqtobe","Asia/Tashkent|Asia/Ashgabat","Asia/Tashkent|Asia/Ashkhabad","Asia/Tashkent|Asia/Atyrau","Asia/Tashkent|Asia/Dushanbe","Asia/Tashkent|Asia/Oral","Asia/Tashkent|Asia/Samarkand","Asia/Tashkent|Asia/Yekaterinburg","Asia/Tashkent|Etc/GMT-5","Asia/Tashkent|Indian/Kerguelen","Asia/Tashkent|Indian/Maldives","Asia/Tehran|Iran","Asia/Tokyo|Japan","Asia/Ulaanbaatar|Asia/Choibalsan","Asia/Ulaanbaatar|Asia/Ulan_Bator","Asia/Vladivostok|Antarctica/DumontDUrville","Asia/Vladivostok|Asia/Ust-Nera","Asia/Vladivostok|Etc/GMT-10","Asia/Vladivostok|Pacific/Chuuk","Asia/Vladivostok|Pacific/Port_Moresby","Asia/Vladivostok|Pacific/Truk","Asia/Vladivostok|Pacific/Yap","Asia/Yakutsk|Asia/Dili","Asia/Yakutsk|Asia/Khandyga","Asia/Yakutsk|Etc/GMT-9","Asia/Yakutsk|Pacific/Palau","Atlantic/Azores|America/Scoresbysund","Atlantic/Cape_Verde|Etc/GMT+1","Australia/Adelaide|Australia/Broken_Hill","Australia/Adelaide|Australia/South","Australia/Adelaide|Australia/Yancowinna","Australia/Brisbane|Australia/Lindeman","Australia/Brisbane|Australia/Queensland","Australia/Darwin|Australia/North","Australia/Lord_Howe|Australia/LHI","Australia/Perth|Australia/West","Australia/Sydney|Australia/ACT","Australia/Sydney|Australia/Canberra","Australia/Sydney|Australia/Currie","Australia/Sydney|Australia/Hobart","Australia/Sydney|Australia/Melbourne","Australia/Sydney|Australia/NSW","Australia/Sydney|Australia/Tasmania","Australia/Sydney|Australia/Victoria","Etc/UTC|Etc/UCT","Etc/UTC|Etc/Universal","Etc/UTC|Etc/Zulu","Etc/UTC|UCT","Etc/UTC|UTC","Etc/UTC|Universal","Etc/UTC|Zulu","Europe/Athens|Asia/Nicosia","Europe/Athens|EET","Europe/Athens|Europe/Bucharest","Europe/Athens|Europe/Helsinki","Europe/Athens|Europe/Kiev","Europe/Athens|Europe/Mariehamn","Europe/Athens|Europe/Nicosia","Europe/Athens|Europe/Riga","Europe/Athens|Europe/Sofia","Europe/Athens|Europe/Tallinn","Europe/Athens|Europe/Uzhgorod","Europe/Athens|Europe/Vilnius","Europe/Athens|Europe/Zaporozhye","Europe/Chisinau|Europe/Tiraspol","Europe/Dublin|Eire","Europe/Istanbul|Asia/Istanbul","Europe/Istanbul|Turkey","Europe/Lisbon|Atlantic/Canary","Europe/Lisbon|Atlantic/Faeroe","Europe/Lisbon|Atlantic/Faroe","Europe/Lisbon|Atlantic/Madeira","Europe/Lisbon|Portugal","Europe/Lisbon|WET","Europe/London|Europe/Belfast","Europe/London|Europe/Guernsey","Europe/London|Europe/Isle_of_Man","Europe/London|Europe/Jersey","Europe/London|GB","Europe/London|GB-Eire","Europe/Moscow|Europe/Simferopol","Europe/Moscow|W-SU","Europe/Paris|Africa/Ceuta","Europe/Paris|Arctic/Longyearbyen","Europe/Paris|Atlantic/Jan_Mayen","Europe/Paris|CET","Europe/Paris|Europe/Amsterdam","Europe/Paris|Europe/Andorra","Europe/Paris|Europe/Belgrade","Europe/Paris|Europe/Berlin","Europe/Paris|Europe/Bratislava","Europe/Paris|Europe/Brussels","Europe/Paris|Europe/Budapest","Europe/Paris|Europe/Busingen","Europe/Paris|Europe/Copenhagen","Europe/Paris|Europe/Gibraltar","Europe/Paris|Europe/Ljubljana","Europe/Paris|Europe/Luxembourg","Europe/Paris|Europe/Madrid","Europe/Paris|Europe/Malta","Europe/Paris|Europe/Monaco","Europe/Paris|Europe/Oslo","Europe/Paris|Europe/Podgorica","Europe/Paris|Europe/Prague","Europe/Paris|Europe/Rome","Europe/Paris|Europe/San_Marino","Europe/Paris|Europe/Sarajevo","Europe/Paris|Europe/Skopje","Europe/Paris|Europe/Stockholm","Europe/Paris|Europe/Tirane","Europe/Paris|Europe/Vaduz","Europe/Paris|Europe/Vatican","Europe/Paris|Europe/Vienna","Europe/Paris|Europe/Warsaw","Europe/Paris|Europe/Zagreb","Europe/Paris|Europe/Zurich","Europe/Paris|Poland","Europe/Ulyanovsk|Europe/Astrakhan","Pacific/Auckland|Antarctica/McMurdo","Pacific/Auckland|Antarctica/South_Pole","Pacific/Auckland|NZ","Pacific/Bougainville|Antarctica/Macquarie","Pacific/Bougainville|Asia/Srednekolymsk","Pacific/Bougainville|Etc/GMT-11","Pacific/Bougainville|Pacific/Efate","Pacific/Bougainville|Pacific/Guadalcanal","Pacific/Bougainville|Pacific/Kosrae","Pacific/Bougainville|Pacific/Noumea","Pacific/Bougainville|Pacific/Pohnpei","Pacific/Bougainville|Pacific/Ponape","Pacific/Chatham|NZ-CHAT","Pacific/Easter|Chile/EasterIsland","Pacific/Fakaofo|Etc/GMT-13","Pacific/Fakaofo|Pacific/Enderbury","Pacific/Galapagos|Etc/GMT+6","Pacific/Gambier|Etc/GMT+9","Pacific/Guam|Pacific/Saipan","Pacific/Honolulu|HST","Pacific/Honolulu|Pacific/Johnston","Pacific/Honolulu|US/Hawaii","Pacific/Kiritimati|Etc/GMT-14","Pacific/Niue|Etc/GMT+11","Pacific/Pago_Pago|Pacific/Midway","Pacific/Pago_Pago|Pacific/Samoa","Pacific/Pago_Pago|US/Samoa","Pacific/Pitcairn|Etc/GMT+8","Pacific/Tahiti|Etc/GMT+10","Pacific/Tahiti|Pacific/Rarotonga"],countries:["AD|Europe/Andorra","AE|Asia/Dubai","AF|Asia/Kabul","AG|America/Port_of_Spain America/Antigua","AI|America/Port_of_Spain America/Anguilla","AL|Europe/Tirane","AM|Asia/Yerevan","AO|Africa/Lagos Africa/Luanda","AQ|Antarctica/Casey Antarctica/Davis Antarctica/DumontDUrville Antarctica/Mawson Antarctica/Palmer Antarctica/Rothera Antarctica/Syowa Antarctica/Troll Antarctica/Vostok Pacific/Auckland Antarctica/McMurdo","AR|America/Argentina/Buenos_Aires America/Argentina/Cordoba America/Argentina/Salta America/Argentina/Jujuy America/Argentina/Tucuman America/Argentina/Catamarca America/Argentina/La_Rioja America/Argentina/San_Juan America/Argentina/Mendoza America/Argentina/San_Luis America/Argentina/Rio_Gallegos America/Argentina/Ushuaia","AS|Pacific/Pago_Pago","AT|Europe/Vienna","AU|Australia/Lord_Howe Antarctica/Macquarie Australia/Hobart Australia/Currie Australia/Melbourne Australia/Sydney Australia/Broken_Hill Australia/Brisbane Australia/Lindeman Australia/Adelaide Australia/Darwin Australia/Perth Australia/Eucla","AW|America/Curacao America/Aruba","AX|Europe/Helsinki Europe/Mariehamn","AZ|Asia/Baku","BA|Europe/Belgrade Europe/Sarajevo","BB|America/Barbados","BD|Asia/Dhaka","BE|Europe/Brussels","BF|Africa/Abidjan Africa/Ouagadougou","BG|Europe/Sofia","BH|Asia/Qatar Asia/Bahrain","BI|Africa/Maputo Africa/Bujumbura","BJ|Africa/Lagos Africa/Porto-Novo","BL|America/Port_of_Spain America/St_Barthelemy","BM|Atlantic/Bermuda","BN|Asia/Brunei","BO|America/La_Paz","BQ|America/Curacao America/Kralendijk","BR|America/Noronha America/Belem America/Fortaleza America/Recife America/Araguaina America/Maceio America/Bahia America/Sao_Paulo America/Campo_Grande America/Cuiaba America/Santarem America/Porto_Velho America/Boa_Vista America/Manaus America/Eirunepe America/Rio_Branco","BS|America/Nassau","BT|Asia/Thimphu","BW|Africa/Maputo Africa/Gaborone","BY|Europe/Minsk","BZ|America/Belize","CA|America/St_Johns America/Halifax America/Glace_Bay America/Moncton America/Goose_Bay America/Blanc-Sablon America/Toronto America/Nipigon America/Thunder_Bay America/Iqaluit America/Pangnirtung America/Atikokan America/Winnipeg America/Rainy_River America/Resolute America/Rankin_Inlet America/Regina America/Swift_Current America/Edmonton America/Cambridge_Bay America/Yellowknife America/Inuvik America/Creston America/Dawson_Creek America/Fort_Nelson America/Vancouver America/Whitehorse America/Dawson","CC|Indian/Cocos","CD|Africa/Maputo Africa/Lagos Africa/Kinshasa Africa/Lubumbashi","CF|Africa/Lagos Africa/Bangui","CG|Africa/Lagos Africa/Brazzaville","CH|Europe/Zurich","CI|Africa/Abidjan","CK|Pacific/Rarotonga","CL|America/Santiago America/Punta_Arenas Pacific/Easter","CM|Africa/Lagos Africa/Douala","CN|Asia/Shanghai Asia/Urumqi","CO|America/Bogota","CR|America/Costa_Rica","CU|America/Havana","CV|Atlantic/Cape_Verde","CW|America/Curacao","CX|Indian/Christmas","CY|Asia/Nicosia Asia/Famagusta","CZ|Europe/Prague","DE|Europe/Zurich Europe/Berlin Europe/Busingen","DJ|Africa/Nairobi Africa/Djibouti","DK|Europe/Copenhagen","DM|America/Port_of_Spain America/Dominica","DO|America/Santo_Domingo","DZ|Africa/Algiers","EC|America/Guayaquil Pacific/Galapagos","EE|Europe/Tallinn","EG|Africa/Cairo","EH|Africa/El_Aaiun","ER|Africa/Nairobi Africa/Asmara","ES|Europe/Madrid Africa/Ceuta Atlantic/Canary","ET|Africa/Nairobi Africa/Addis_Ababa","FI|Europe/Helsinki","FJ|Pacific/Fiji","FK|Atlantic/Stanley","FM|Pacific/Chuuk Pacific/Pohnpei Pacific/Kosrae","FO|Atlantic/Faroe","FR|Europe/Paris","GA|Africa/Lagos Africa/Libreville","GB|Europe/London","GD|America/Port_of_Spain America/Grenada","GE|Asia/Tbilisi","GF|America/Cayenne","GG|Europe/London Europe/Guernsey","GH|Africa/Accra","GI|Europe/Gibraltar","GL|America/Godthab America/Danmarkshavn America/Scoresbysund America/Thule","GM|Africa/Abidjan Africa/Banjul","GN|Africa/Abidjan Africa/Conakry","GP|America/Port_of_Spain America/Guadeloupe","GQ|Africa/Lagos Africa/Malabo","GR|Europe/Athens","GS|Atlantic/South_Georgia","GT|America/Guatemala","GU|Pacific/Guam","GW|Africa/Bissau","GY|America/Guyana","HK|Asia/Hong_Kong","HN|America/Tegucigalpa","HR|Europe/Belgrade Europe/Zagreb","HT|America/Port-au-Prince","HU|Europe/Budapest","ID|Asia/Jakarta Asia/Pontianak Asia/Makassar Asia/Jayapura","IE|Europe/Dublin","IL|Asia/Jerusalem","IM|Europe/London Europe/Isle_of_Man","IN|Asia/Kolkata","IO|Indian/Chagos","IQ|Asia/Baghdad","IR|Asia/Tehran","IS|Atlantic/Reykjavik","IT|Europe/Rome","JE|Europe/London Europe/Jersey","JM|America/Jamaica","JO|Asia/Amman","JP|Asia/Tokyo","KE|Africa/Nairobi","KG|Asia/Bishkek","KH|Asia/Bangkok Asia/Phnom_Penh","KI|Pacific/Tarawa Pacific/Enderbury Pacific/Kiritimati","KM|Africa/Nairobi Indian/Comoro","KN|America/Port_of_Spain America/St_Kitts","KP|Asia/Pyongyang","KR|Asia/Seoul","KW|Asia/Riyadh Asia/Kuwait","KY|America/Panama America/Cayman","KZ|Asia/Almaty Asia/Qyzylorda Asia/Qostanay Asia/Aqtobe Asia/Aqtau Asia/Atyrau Asia/Oral","LA|Asia/Bangkok Asia/Vientiane","LB|Asia/Beirut","LC|America/Port_of_Spain America/St_Lucia","LI|Europe/Zurich Europe/Vaduz","LK|Asia/Colombo","LR|Africa/Monrovia","LS|Africa/Johannesburg Africa/Maseru","LT|Europe/Vilnius","LU|Europe/Luxembourg","LV|Europe/Riga","LY|Africa/Tripoli","MA|Africa/Casablanca","MC|Europe/Monaco","MD|Europe/Chisinau","ME|Europe/Belgrade Europe/Podgorica","MF|America/Port_of_Spain America/Marigot","MG|Africa/Nairobi Indian/Antananarivo","MH|Pacific/Majuro Pacific/Kwajalein","MK|Europe/Belgrade Europe/Skopje","ML|Africa/Abidjan Africa/Bamako","MM|Asia/Yangon","MN|Asia/Ulaanbaatar Asia/Hovd Asia/Choibalsan","MO|Asia/Macau","MP|Pacific/Guam Pacific/Saipan","MQ|America/Martinique","MR|Africa/Abidjan Africa/Nouakchott","MS|America/Port_of_Spain America/Montserrat","MT|Europe/Malta","MU|Indian/Mauritius","MV|Indian/Maldives","MW|Africa/Maputo Africa/Blantyre","MX|America/Mexico_City America/Cancun America/Merida America/Monterrey America/Matamoros America/Mazatlan America/Chihuahua America/Ojinaga America/Hermosillo America/Tijuana America/Bahia_Banderas","MY|Asia/Kuala_Lumpur Asia/Kuching","MZ|Africa/Maputo","NA|Africa/Windhoek","NC|Pacific/Noumea","NE|Africa/Lagos Africa/Niamey","NF|Pacific/Norfolk","NG|Africa/Lagos","NI|America/Managua","NL|Europe/Amsterdam","NO|Europe/Oslo","NP|Asia/Kathmandu","NR|Pacific/Nauru","NU|Pacific/Niue","NZ|Pacific/Auckland Pacific/Chatham","OM|Asia/Dubai Asia/Muscat","PA|America/Panama","PE|America/Lima","PF|Pacific/Tahiti Pacific/Marquesas Pacific/Gambier","PG|Pacific/Port_Moresby Pacific/Bougainville","PH|Asia/Manila","PK|Asia/Karachi","PL|Europe/Warsaw","PM|America/Miquelon","PN|Pacific/Pitcairn","PR|America/Puerto_Rico","PS|Asia/Gaza Asia/Hebron","PT|Europe/Lisbon Atlantic/Madeira Atlantic/Azores","PW|Pacific/Palau","PY|America/Asuncion","QA|Asia/Qatar","RE|Indian/Reunion","RO|Europe/Bucharest","RS|Europe/Belgrade","RU|Europe/Kaliningrad Europe/Moscow Europe/Simferopol Europe/Kirov Europe/Astrakhan Europe/Volgograd Europe/Saratov Europe/Ulyanovsk Europe/Samara Asia/Yekaterinburg Asia/Omsk Asia/Novosibirsk Asia/Barnaul Asia/Tomsk Asia/Novokuznetsk Asia/Krasnoyarsk Asia/Irkutsk Asia/Chita Asia/Yakutsk Asia/Khandyga Asia/Vladivostok Asia/Ust-Nera Asia/Magadan Asia/Sakhalin Asia/Srednekolymsk Asia/Kamchatka Asia/Anadyr","RW|Africa/Maputo Africa/Kigali","SA|Asia/Riyadh","SB|Pacific/Guadalcanal","SC|Indian/Mahe","SD|Africa/Khartoum","SE|Europe/Stockholm","SG|Asia/Singapore","SH|Africa/Abidjan Atlantic/St_Helena","SI|Europe/Belgrade Europe/Ljubljana","SJ|Europe/Oslo Arctic/Longyearbyen","SK|Europe/Prague Europe/Bratislava","SL|Africa/Abidjan Africa/Freetown","SM|Europe/Rome Europe/San_Marino","SN|Africa/Abidjan Africa/Dakar","SO|Africa/Nairobi Africa/Mogadishu","SR|America/Paramaribo","SS|Africa/Juba","ST|Africa/Sao_Tome","SV|America/El_Salvador","SX|America/Curacao America/Lower_Princes","SY|Asia/Damascus","SZ|Africa/Johannesburg Africa/Mbabane","TC|America/Grand_Turk","TD|Africa/Ndjamena","TF|Indian/Reunion Indian/Kerguelen","TG|Africa/Abidjan Africa/Lome","TH|Asia/Bangkok","TJ|Asia/Dushanbe","TK|Pacific/Fakaofo","TL|Asia/Dili","TM|Asia/Ashgabat","TN|Africa/Tunis","TO|Pacific/Tongatapu","TR|Europe/Istanbul","TT|America/Port_of_Spain","TV|Pacific/Funafuti","TW|Asia/Taipei","TZ|Africa/Nairobi Africa/Dar_es_Salaam","UA|Europe/Simferopol Europe/Kiev Europe/Uzhgorod Europe/Zaporozhye","UG|Africa/Nairobi Africa/Kampala","UM|Pacific/Pago_Pago Pacific/Wake Pacific/Honolulu Pacific/Midway","US|America/New_York America/Detroit America/Kentucky/Louisville America/Kentucky/Monticello America/Indiana/Indianapolis America/Indiana/Vincennes America/Indiana/Winamac America/Indiana/Marengo America/Indiana/Petersburg America/Indiana/Vevay America/Chicago America/Indiana/Tell_City America/Indiana/Knox America/Menominee America/North_Dakota/Center America/North_Dakota/New_Salem America/North_Dakota/Beulah America/Denver America/Boise America/Phoenix America/Los_Angeles America/Anchorage America/Juneau America/Sitka America/Metlakatla America/Yakutat America/Nome America/Adak Pacific/Honolulu","UY|America/Montevideo","UZ|Asia/Samarkand Asia/Tashkent","VA|Europe/Rome Europe/Vatican","VC|America/Port_of_Spain America/St_Vincent","VE|America/Caracas","VG|America/Port_of_Spain America/Tortola","VI|America/Port_of_Spain America/St_Thomas","VN|Asia/Bangkok Asia/Ho_Chi_Minh","VU|Pacific/Efate","WF|Pacific/Wallis","WS|Pacific/Apia","YE|Asia/Riyadh Asia/Aden","YT|Africa/Nairobi Indian/Mayotte","ZA|Africa/Johannesburg","ZM|Africa/Maputo Africa/Lusaka","ZW|Africa/Maputo Africa/Harare"]}),c});
